/*!
 * jQuery JavaScript Library v3.2.1
 * https://jquery.com/
 *
 * Includes Sizzle.js
 * https://sizzlejs.com/
 *
 * Copyright JS Foundation and other contributors
 * Released under the MIT license
 * https://jquery.org/license
 *
 * Date: 2017-03-20T18:59Z
 */
( function( global, factory ) {

	"use strict";

	if ( typeof module === "object" && typeof module.exports === "object" ) {

		// For CommonJS and CommonJS-like environments where a proper `window`
		// is present, execute the factory and get jQuery.
		// For environments that do not have a `window` with a `document`
		// (such as Node.js), expose a factory as module.exports.
		// This accentuates the need for the creation of a real `window`.
		// e.g. var jQuery = require("jquery")(window);
		// See ticket #14549 for more info.
		module.exports = global.document ?
			factory( global, true ) :
			function( w ) {
				if ( !w.document ) {
					throw new Error( "jQuery requires a window with a document" );
				}
				return factory( w );
			};
	} else {
		factory( global );
	}

// Pass this if window is not defined yet
} )( typeof window !== "undefined" ? window : this, function( window, noGlobal ) {

// Edge <= 12 - 13+, Firefox <=18 - 45+, IE 10 - 11, Safari 5.1 - 9+, iOS 6 - 9.1
// throw exceptions when non-strict code (e.g., ASP.NET 4.5) accesses strict mode
// arguments.callee.caller (trac-13335). But as of jQuery 3.0 (2016), strict mode should be common
// enough that all such attempts are guarded in a try block.
"use strict";

var arr = [];

var document = window.document;

var getProto = Object.getPrototypeOf;

var slice = arr.slice;

var concat = arr.concat;

var push = arr.push;

var indexOf = arr.indexOf;

var class2type = {};

var toString = class2type.toString;

var hasOwn = class2type.hasOwnProperty;

var fnToString = hasOwn.toString;

var ObjectFunctionString = fnToString.call( Object );

var support = {};



	function DOMEval( code, doc ) {
		doc = doc || document;

		var script = doc.createElement( "script" );

		script.text = code;
		doc.head.appendChild( script ).parentNode.removeChild( script );
	}
/* global Symbol */
// Defining this global in .eslintrc.json would create a danger of using the global
// unguarded in another place, it seems safer to define global only for this module



var
	version = "3.2.1",

	// Define a local copy of jQuery
	jQuery = function( selector, context ) {

		// The jQuery object is actually just the init constructor 'enhanced'
		// Need init if jQuery is called (just allow error to be thrown if not included)
		return new jQuery.fn.init( selector, context );
	},

	// Support: Android <=4.0 only
	// Make sure we trim BOM and NBSP
	rtrim = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g,

	// Matches dashed string for camelizing
	rmsPrefix = /^-ms-/,
	rdashAlpha = /-([a-z])/g,

	// Used by jQuery.camelCase as callback to replace()
	fcamelCase = function( all, letter ) {
		return letter.toUpperCase();
	};

jQuery.fn = jQuery.prototype = {

	// The current version of jQuery being used
	jquery: version,

	constructor: jQuery,

	// The default length of a jQuery object is 0
	length: 0,

	toArray: function() {
		return slice.call( this );
	},

	// Get the Nth element in the matched element set OR
	// Get the whole matched element set as a clean array
	get: function( num ) {

		// Return all the elements in a clean array
		if ( num == null ) {
			return slice.call( this );
		}

		// Return just the one element from the set
		return num < 0 ? this[ num + this.length ] : this[ num ];
	},

	// Take an array of elements and push it onto the stack
	// (returning the new matched element set)
	pushStack: function( elems ) {

		// Build a new jQuery matched element set
		var ret = jQuery.merge( this.constructor(), elems );

		// Add the old object onto the stack (as a reference)
		ret.prevObject = this;

		// Return the newly-formed element set
		return ret;
	},

	// Execute a callback for every element in the matched set.
	each: function( callback ) {
		return jQuery.each( this, callback );
	},

	map: function( callback ) {
		return this.pushStack( jQuery.map( this, function( elem, i ) {
			return callback.call( elem, i, elem );
		} ) );
	},

	slice: function() {
		return this.pushStack( slice.apply( this, arguments ) );
	},

	first: function() {
		return this.eq( 0 );
	},

	last: function() {
		return this.eq( -1 );
	},

	eq: function( i ) {
		var len = this.length,
			j = +i + ( i < 0 ? len : 0 );
		return this.pushStack( j >= 0 && j < len ? [ this[ j ] ] : [] );
	},

	end: function() {
		return this.prevObject || this.constructor();
	},

	// For internal use only.
	// Behaves like an Array's method, not like a jQuery method.
	push: push,
	sort: arr.sort,
	splice: arr.splice
};

jQuery.extend = jQuery.fn.extend = function() {
	var options, name, src, copy, copyIsArray, clone,
		target = arguments[ 0 ] || {},
		i = 1,
		length = arguments.length,
		deep = false;

	// Handle a deep copy situation
	if ( typeof target === "boolean" ) {
		deep = target;

		// Skip the boolean and the target
		target = arguments[ i ] || {};
		i++;
	}

	// Handle case when target is a string or something (possible in deep copy)
	if ( typeof target !== "object" && !jQuery.isFunction( target ) ) {
		target = {};
	}

	// Extend jQuery itself if only one argument is passed
	if ( i === length ) {
		target = this;
		i--;
	}

	for ( ; i < length; i++ ) {

		// Only deal with non-null/undefined values
		if ( ( options = arguments[ i ] ) != null ) {

			// Extend the base object
			for ( name in options ) {
				src = target[ name ];
				copy = options[ name ];

				// Prevent never-ending loop
				if ( target === copy ) {
					continue;
				}

				// Recurse if we're merging plain objects or arrays
				if ( deep && copy && ( jQuery.isPlainObject( copy ) ||
					( copyIsArray = Array.isArray( copy ) ) ) ) {

					if ( copyIsArray ) {
						copyIsArray = false;
						clone = src && Array.isArray( src ) ? src : [];

					} else {
						clone = src && jQuery.isPlainObject( src ) ? src : {};
					}

					// Never move original objects, clone them
					target[ name ] = jQuery.extend( deep, clone, copy );

				// Don't bring in undefined values
				} else if ( copy !== undefined ) {
					target[ name ] = copy;
				}
			}
		}
	}

	// Return the modified object
	return target;
};

jQuery.extend( {

	// Unique for each copy of jQuery on the page
	expando: "jQuery" + ( version + Math.random() ).replace( /\D/g, "" ),

	// Assume jQuery is ready without the ready module
	isReady: true,

	error: function( msg ) {
		throw new Error( msg );
	},

	noop: function() {},

	isFunction: function( obj ) {
		return jQuery.type( obj ) === "function";
	},

	isWindow: function( obj ) {
		return obj != null && obj === obj.window;
	},

	isNumeric: function( obj ) {

		// As of jQuery 3.0, isNumeric is limited to
		// strings and numbers (primitives or objects)
		// that can be coerced to finite numbers (gh-2662)
		var type = jQuery.type( obj );
		return ( type === "number" || type === "string" ) &&

			// parseFloat NaNs numeric-cast false positives ("")
			// ...but misinterprets leading-number strings, particularly hex literals ("0x...")
			// subtraction forces infinities to NaN
			!isNaN( obj - parseFloat( obj ) );
	},

	isPlainObject: function( obj ) {
		var proto, Ctor;

		// Detect obvious negatives
		// Use toString instead of jQuery.type to catch host objects
		if ( !obj || toString.call( obj ) !== "[object Object]" ) {
			return false;
		}

		proto = getProto( obj );

		// Objects with no prototype (e.g., `Object.create( null )`) are plain
		if ( !proto ) {
			return true;
		}

		// Objects with prototype are plain iff they were constructed by a global Object function
		Ctor = hasOwn.call( proto, "constructor" ) && proto.constructor;
		return typeof Ctor === "function" && fnToString.call( Ctor ) === ObjectFunctionString;
	},

	isEmptyObject: function( obj ) {

		/* eslint-disable no-unused-vars */
		// See https://github.com/eslint/eslint/issues/6125
		var name;

		for ( name in obj ) {
			return false;
		}
		return true;
	},

	type: function( obj ) {
		if ( obj == null ) {
			return obj + "";
		}

		// Support: Android <=2.3 only (functionish RegExp)
		return typeof obj === "object" || typeof obj === "function" ?
			class2type[ toString.call( obj ) ] || "object" :
			typeof obj;
	},

	// Evaluates a script in a global context
	globalEval: function( code ) {
		DOMEval( code );
	},

	// Convert dashed to camelCase; used by the css and data modules
	// Support: IE <=9 - 11, Edge 12 - 13
	// Microsoft forgot to hump their vendor prefix (#9572)
	camelCase: function( string ) {
		return string.replace( rmsPrefix, "ms-" ).replace( rdashAlpha, fcamelCase );
	},

	each: function( obj, callback ) {
		var length, i = 0;

		if ( isArrayLike( obj ) ) {
			length = obj.length;
			for ( ; i < length; i++ ) {
				if ( callback.call( obj[ i ], i, obj[ i ] ) === false ) {
					break;
				}
			}
		} else {
			for ( i in obj ) {
				if ( callback.call( obj[ i ], i, obj[ i ] ) === false ) {
					break;
				}
			}
		}

		return obj;
	},

	// Support: Android <=4.0 only
	trim: function( text ) {
		return text == null ?
			"" :
			( text + "" ).replace( rtrim, "" );
	},

	// results is for internal usage only
	makeArray: function( arr, results ) {
		var ret = results || [];

		if ( arr != null ) {
			if ( isArrayLike( Object( arr ) ) ) {
				jQuery.merge( ret,
					typeof arr === "string" ?
					[ arr ] : arr
				);
			} else {
				push.call( ret, arr );
			}
		}

		return ret;
	},

	inArray: function( elem, arr, i ) {
		return arr == null ? -1 : indexOf.call( arr, elem, i );
	},

	// Support: Android <=4.0 only, PhantomJS 1 only
	// push.apply(_, arraylike) throws on ancient WebKit
	merge: function( first, second ) {
		var len = +second.length,
			j = 0,
			i = first.length;

		for ( ; j < len; j++ ) {
			first[ i++ ] = second[ j ];
		}

		first.length = i;

		return first;
	},

	grep: function( elems, callback, invert ) {
		var callbackInverse,
			matches = [],
			i = 0,
			length = elems.length,
			callbackExpect = !invert;

		// Go through the array, only saving the items
		// that pass the validator function
		for ( ; i < length; i++ ) {
			callbackInverse = !callback( elems[ i ], i );
			if ( callbackInverse !== callbackExpect ) {
				matches.push( elems[ i ] );
			}
		}

		return matches;
	},

	// arg is for internal usage only
	map: function( elems, callback, arg ) {
		var length, value,
			i = 0,
			ret = [];

		// Go through the array, translating each of the items to their new values
		if ( isArrayLike( elems ) ) {
			length = elems.length;
			for ( ; i < length; i++ ) {
				value = callback( elems[ i ], i, arg );

				if ( value != null ) {
					ret.push( value );
				}
			}

		// Go through every key on the object,
		} else {
			for ( i in elems ) {
				value = callback( elems[ i ], i, arg );

				if ( value != null ) {
					ret.push( value );
				}
			}
		}

		// Flatten any nested arrays
		return concat.apply( [], ret );
	},

	// A global GUID counter for objects
	guid: 1,

	// Bind a function to a context, optionally partially applying any
	// arguments.
	proxy: function( fn, context ) {
		var tmp, args, proxy;

		if ( typeof context === "string" ) {
			tmp = fn[ context ];
			context = fn;
			fn = tmp;
		}

		// Quick check to determine if target is callable, in the spec
		// this throws a TypeError, but we will just return undefined.
		if ( !jQuery.isFunction( fn ) ) {
			return undefined;
		}

		// Simulated bind
		args = slice.call( arguments, 2 );
		proxy = function() {
			return fn.apply( context || this, args.concat( slice.call( arguments ) ) );
		};

		// Set the guid of unique handler to the same of original handler, so it can be removed
		proxy.guid = fn.guid = fn.guid || jQuery.guid++;

		return proxy;
	},

	now: Date.now,

	// jQuery.support is not used in Core but other projects attach their
	// properties to it so it needs to exist.
	support: support
} );

if ( typeof Symbol === "function" ) {
	jQuery.fn[ Symbol.iterator ] = arr[ Symbol.iterator ];
}

// Populate the class2type map
jQuery.each( "Boolean Number String Function Array Date RegExp Object Error Symbol".split( " " ),
function( i, name ) {
	class2type[ "[object " + name + "]" ] = name.toLowerCase();
} );

function isArrayLike( obj ) {

	// Support: real iOS 8.2 only (not reproducible in simulator)
	// `in` check used to prevent JIT error (gh-2145)
	// hasOwn isn't used here due to false negatives
	// regarding Nodelist length in IE
	var length = !!obj && "length" in obj && obj.length,
		type = jQuery.type( obj );

	if ( type === "function" || jQuery.isWindow( obj ) ) {
		return false;
	}

	return type === "array" || length === 0 ||
		typeof length === "number" && length > 0 && ( length - 1 ) in obj;
}
var Sizzle =
/*!
 * Sizzle CSS Selector Engine v2.3.3
 * https://sizzlejs.com/
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license
 * http://jquery.org/license
 *
 * Date: 2016-08-08
 */
(function( window ) {

var i,
	support,
	Expr,
	getText,
	isXML,
	tokenize,
	compile,
	select,
	outermostContext,
	sortInput,
	hasDuplicate,

	// Local document vars
	setDocument,
	document,
	docElem,
	documentIsHTML,
	rbuggyQSA,
	rbuggyMatches,
	matches,
	contains,

	// Instance-specific data
	expando = "sizzle" + 1 * new Date(),
	preferredDoc = window.document,
	dirruns = 0,
	done = 0,
	classCache = createCache(),
	tokenCache = createCache(),
	compilerCache = createCache(),
	sortOrder = function( a, b ) {
		if ( a === b ) {
			hasDuplicate = true;
		}
		return 0;
	},

	// Instance methods
	hasOwn = ({}).hasOwnProperty,
	arr = [],
	pop = arr.pop,
	push_native = arr.push,
	push = arr.push,
	slice = arr.slice,
	// Use a stripped-down indexOf as it's faster than native
	// https://jsperf.com/thor-indexof-vs-for/5
	indexOf = function( list, elem ) {
		var i = 0,
			len = list.length;
		for ( ; i < len; i++ ) {
			if ( list[i] === elem ) {
				return i;
			}
		}
		return -1;
	},

	booleans = "checked|selected|async|autofocus|autoplay|controls|defer|disabled|hidden|ismap|loop|multiple|open|readonly|required|scoped",

	// Regular expressions

	// http://www.w3.org/TR/css3-selectors/#whitespace
	whitespace = "[\\x20\\t\\r\\n\\f]",

	// http://www.w3.org/TR/CSS21/syndata.html#value-def-identifier
	identifier = "(?:\\\\.|[\\w-]|[^\0-\\xa0])+",

	// Attribute selectors: http://www.w3.org/TR/selectors/#attribute-selectors
	attributes = "\\[" + whitespace + "*(" + identifier + ")(?:" + whitespace +
		// Operator (capture 2)
		"*([*^$|!~]?=)" + whitespace +
		// "Attribute values must be CSS identifiers [capture 5] or strings [capture 3 or capture 4]"
		"*(?:'((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\"|(" + identifier + "))|)" + whitespace +
		"*\\]",

	pseudos = ":(" + identifier + ")(?:\\((" +
		// To reduce the number of selectors needing tokenize in the preFilter, prefer arguments:
		// 1. quoted (capture 3; capture 4 or capture 5)
		"('((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\")|" +
		// 2. simple (capture 6)
		"((?:\\\\.|[^\\\\()[\\]]|" + attributes + ")*)|" +
		// 3. anything else (capture 2)
		".*" +
		")\\)|)",

	// Leading and non-escaped trailing whitespace, capturing some non-whitespace characters preceding the latter
	rwhitespace = new RegExp( whitespace + "+", "g" ),
	rtrim = new RegExp( "^" + whitespace + "+|((?:^|[^\\\\])(?:\\\\.)*)" + whitespace + "+$", "g" ),

	rcomma = new RegExp( "^" + whitespace + "*," + whitespace + "*" ),
	rcombinators = new RegExp( "^" + whitespace + "*([>+~]|" + whitespace + ")" + whitespace + "*" ),

	rattributeQuotes = new RegExp( "=" + whitespace + "*([^\\]'\"]*?)" + whitespace + "*\\]", "g" ),

	rpseudo = new RegExp( pseudos ),
	ridentifier = new RegExp( "^" + identifier + "$" ),

	matchExpr = {
		"ID": new RegExp( "^#(" + identifier + ")" ),
		"CLASS": new RegExp( "^\\.(" + identifier + ")" ),
		"TAG": new RegExp( "^(" + identifier + "|[*])" ),
		"ATTR": new RegExp( "^" + attributes ),
		"PSEUDO": new RegExp( "^" + pseudos ),
		"CHILD": new RegExp( "^:(only|first|last|nth|nth-last)-(child|of-type)(?:\\(" + whitespace +
			"*(even|odd|(([+-]|)(\\d*)n|)" + whitespace + "*(?:([+-]|)" + whitespace +
			"*(\\d+)|))" + whitespace + "*\\)|)", "i" ),
		"bool": new RegExp( "^(?:" + booleans + ")$", "i" ),
		// For use in libraries implementing .is()
		// We use this for POS matching in `select`
		"needsContext": new RegExp( "^" + whitespace + "*[>+~]|:(even|odd|eq|gt|lt|nth|first|last)(?:\\(" +
			whitespace + "*((?:-\\d)?\\d*)" + whitespace + "*\\)|)(?=[^-]|$)", "i" )
	},

	rinputs = /^(?:input|select|textarea|button)$/i,
	rheader = /^h\d$/i,

	rnative = /^[^{]+\{\s*\[native \w/,

	// Easily-parseable/retrievable ID or TAG or CLASS selectors
	rquickExpr = /^(?:#([\w-]+)|(\w+)|\.([\w-]+))$/,

	rsibling = /[+~]/,

	// CSS escapes
	// http://www.w3.org/TR/CSS21/syndata.html#escaped-characters
	runescape = new RegExp( "\\\\([\\da-f]{1,6}" + whitespace + "?|(" + whitespace + ")|.)", "ig" ),
	funescape = function( _, escaped, escapedWhitespace ) {
		var high = "0x" + escaped - 0x10000;
		// NaN means non-codepoint
		// Support: Firefox<24
		// Workaround erroneous numeric interpretation of +"0x"
		return high !== high || escapedWhitespace ?
			escaped :
			high < 0 ?
				// BMP codepoint
				String.fromCharCode( high + 0x10000 ) :
				// Supplemental Plane codepoint (surrogate pair)
				String.fromCharCode( high >> 10 | 0xD800, high & 0x3FF | 0xDC00 );
	},

	// CSS string/identifier serialization
	// https://drafts.csswg.org/cssom/#common-serializing-idioms
	rcssescape = /([\0-\x1f\x7f]|^-?\d)|^-$|[^\0-\x1f\x7f-\uFFFF\w-]/g,
	fcssescape = function( ch, asCodePoint ) {
		if ( asCodePoint ) {

			// U+0000 NULL becomes U+FFFD REPLACEMENT CHARACTER
			if ( ch === "\0" ) {
				return "\uFFFD";
			}

			// Control characters and (dependent upon position) numbers get escaped as code points
			return ch.slice( 0, -1 ) + "\\" + ch.charCodeAt( ch.length - 1 ).toString( 16 ) + " ";
		}

		// Other potentially-special ASCII characters get backslash-escaped
		return "\\" + ch;
	},

	// Used for iframes
	// See setDocument()
	// Removing the function wrapper causes a "Permission Denied"
	// error in IE
	unloadHandler = function() {
		setDocument();
	},

	disabledAncestor = addCombinator(
		function( elem ) {
			return elem.disabled === true && ("form" in elem || "label" in elem);
		},
		{ dir: "parentNode", next: "legend" }
	);

// Optimize for push.apply( _, NodeList )
try {
	push.apply(
		(arr = slice.call( preferredDoc.childNodes )),
		preferredDoc.childNodes
	);
	// Support: Android<4.0
	// Detect silently failing push.apply
	arr[ preferredDoc.childNodes.length ].nodeType;
} catch ( e ) {
	push = { apply: arr.length ?

		// Leverage slice if possible
		function( target, els ) {
			push_native.apply( target, slice.call(els) );
		} :

		// Support: IE<9
		// Otherwise append directly
		function( target, els ) {
			var j = target.length,
				i = 0;
			// Can't trust NodeList.length
			while ( (target[j++] = els[i++]) ) {}
			target.length = j - 1;
		}
	};
}

function Sizzle( selector, context, results, seed ) {
	var m, i, elem, nid, match, groups, newSelector,
		newContext = context && context.ownerDocument,

		// nodeType defaults to 9, since context defaults to document
		nodeType = context ? context.nodeType : 9;

	results = results || [];

	// Return early from calls with invalid selector or context
	if ( typeof selector !== "string" || !selector ||
		nodeType !== 1 && nodeType !== 9 && nodeType !== 11 ) {

		return results;
	}

	// Try to shortcut find operations (as opposed to filters) in HTML documents
	if ( !seed ) {

		if ( ( context ? context.ownerDocument || context : preferredDoc ) !== document ) {
			setDocument( context );
		}
		context = context || document;

		if ( documentIsHTML ) {

			// If the selector is sufficiently simple, try using a "get*By*" DOM method
			// (excepting DocumentFragment context, where the methods don't exist)
			if ( nodeType !== 11 && (match = rquickExpr.exec( selector )) ) {

				// ID selector
				if ( (m = match[1]) ) {

					// Document context
					if ( nodeType === 9 ) {
						if ( (elem = context.getElementById( m )) ) {

							// Support: IE, Opera, Webkit
							// TODO: identify versions
							// getElementById can match elements by name instead of ID
							if ( elem.id === m ) {
								results.push( elem );
								return results;
							}
						} else {
							return results;
						}

					// Element context
					} else {

						// Support: IE, Opera, Webkit
						// TODO: identify versions
						// getElementById can match elements by name instead of ID
						if ( newContext && (elem = newContext.getElementById( m )) &&
							contains( context, elem ) &&
							elem.id === m ) {

							results.push( elem );
							return results;
						}
					}

				// Type selector
				} else if ( match[2] ) {
					push.apply( results, context.getElementsByTagName( selector ) );
					return results;

				// Class selector
				} else if ( (m = match[3]) && support.getElementsByClassName &&
					context.getElementsByClassName ) {

					push.apply( results, context.getElementsByClassName( m ) );
					return results;
				}
			}

			// Take advantage of querySelectorAll
			if ( support.qsa &&
				!compilerCache[ selector + " " ] &&
				(!rbuggyQSA || !rbuggyQSA.test( selector )) ) {

				if ( nodeType !== 1 ) {
					newContext = context;
					newSelector = selector;

				// qSA looks outside Element context, which is not what we want
				// Thanks to Andrew Dupont for this workaround technique
				// Support: IE <=8
				// Exclude object elements
				} else if ( context.nodeName.toLowerCase() !== "object" ) {

					// Capture the context ID, setting it first if necessary
					if ( (nid = context.getAttribute( "id" )) ) {
						nid = nid.replace( rcssescape, fcssescape );
					} else {
						context.setAttribute( "id", (nid = expando) );
					}

					// Prefix every selector in the list
					groups = tokenize( selector );
					i = groups.length;
					while ( i-- ) {
						groups[i] = "#" + nid + " " + toSelector( groups[i] );
					}
					newSelector = groups.join( "," );

					// Expand context for sibling selectors
					newContext = rsibling.test( selector ) && testContext( context.parentNode ) ||
						context;
				}

				if ( newSelector ) {
					try {
						push.apply( results,
							newContext.querySelectorAll( newSelector )
						);
						return results;
					} catch ( qsaError ) {
					} finally {
						if ( nid === expando ) {
							context.removeAttribute( "id" );
						}
					}
				}
			}
		}
	}

	// All others
	return select( selector.replace( rtrim, "$1" ), context, results, seed );
}

/**
 * Create key-value caches of limited size
 * @returns {function(string, object)} Returns the Object data after storing it on itself with
 *	property name the (space-suffixed) string and (if the cache is larger than Expr.cacheLength)
 *	deleting the oldest entry
 */
function createCache() {
	var keys = [];

	function cache( key, value ) {
		// Use (key + " ") to avoid collision with native prototype properties (see Issue #157)
		if ( keys.push( key + " " ) > Expr.cacheLength ) {
			// Only keep the most recent entries
			delete cache[ keys.shift() ];
		}
		return (cache[ key + " " ] = value);
	}
	return cache;
}

/**
 * Mark a function for special use by Sizzle
 * @param {Function} fn The function to mark
 */
function markFunction( fn ) {
	fn[ expando ] = true;
	return fn;
}

/**
 * Support testing using an element
 * @param {Function} fn Passed the created element and returns a boolean result
 */
function assert( fn ) {
	var el = document.createElement("fieldset");

	try {
		return !!fn( el );
	} catch (e) {
		return false;
	} finally {
		// Remove from its parent by default
		if ( el.parentNode ) {
			el.parentNode.removeChild( el );
		}
		// release memory in IE
		el = null;
	}
}

/**
 * Adds the same handler for all of the specified attrs
 * @param {String} attrs Pipe-separated list of attributes
 * @param {Function} handler The method that will be applied
 */
function addHandle( attrs, handler ) {
	var arr = attrs.split("|"),
		i = arr.length;

	while ( i-- ) {
		Expr.attrHandle[ arr[i] ] = handler;
	}
}

/**
 * Checks document order of two siblings
 * @param {Element} a
 * @param {Element} b
 * @returns {Number} Returns less than 0 if a precedes b, greater than 0 if a follows b
 */
function siblingCheck( a, b ) {
	var cur = b && a,
		diff = cur && a.nodeType === 1 && b.nodeType === 1 &&
			a.sourceIndex - b.sourceIndex;

	// Use IE sourceIndex if available on both nodes
	if ( diff ) {
		return diff;
	}

	// Check if b follows a
	if ( cur ) {
		while ( (cur = cur.nextSibling) ) {
			if ( cur === b ) {
				return -1;
			}
		}
	}

	return a ? 1 : -1;
}

/**
 * Returns a function to use in pseudos for input types
 * @param {String} type
 */
function createInputPseudo( type ) {
	return function( elem ) {
		var name = elem.nodeName.toLowerCase();
		return name === "input" && elem.type === type;
	};
}

/**
 * Returns a function to use in pseudos for buttons
 * @param {String} type
 */
function createButtonPseudo( type ) {
	return function( elem ) {
		var name = elem.nodeName.toLowerCase();
		return (name === "input" || name === "button") && elem.type === type;
	};
}

/**
 * Returns a function to use in pseudos for :enabled/:disabled
 * @param {Boolean} disabled true for :disabled; false for :enabled
 */
function createDisabledPseudo( disabled ) {

	// Known :disabled false positives: fieldset[disabled] > legend:nth-of-type(n+2) :can-disable
	return function( elem ) {

		// Only certain elements can match :enabled or :disabled
		// https://html.spec.whatwg.org/multipage/scripting.html#selector-enabled
		// https://html.spec.whatwg.org/multipage/scripting.html#selector-disabled
		if ( "form" in elem ) {

			// Check for inherited disabledness on relevant non-disabled elements:
			// * listed form-associated elements in a disabled fieldset
			//   https://html.spec.whatwg.org/multipage/forms.html#category-listed
			//   https://html.spec.whatwg.org/multipage/forms.html#concept-fe-disabled
			// * option elements in a disabled optgroup
			//   https://html.spec.whatwg.org/multipage/forms.html#concept-option-disabled
			// All such elements have a "form" property.
			if ( elem.parentNode && elem.disabled === false ) {

				// Option elements defer to a parent optgroup if present
				if ( "label" in elem ) {
					if ( "label" in elem.parentNode ) {
						return elem.parentNode.disabled === disabled;
					} else {
						return elem.disabled === disabled;
					}
				}

				// Support: IE 6 - 11
				// Use the isDisabled shortcut property to check for disabled fieldset ancestors
				return elem.isDisabled === disabled ||

					// Where there is no isDisabled, check manually
					/* jshint -W018 */
					elem.isDisabled !== !disabled &&
						disabledAncestor( elem ) === disabled;
			}

			return elem.disabled === disabled;

		// Try to winnow out elements that can't be disabled before trusting the disabled property.
		// Some victims get caught in our net (label, legend, menu, track), but it shouldn't
		// even exist on them, let alone have a boolean value.
		} else if ( "label" in elem ) {
			return elem.disabled === disabled;
		}

		// Remaining elements are neither :enabled nor :disabled
		return false;
	};
}

/**
 * Returns a function to use in pseudos for positionals
 * @param {Function} fn
 */
function createPositionalPseudo( fn ) {
	return markFunction(function( argument ) {
		argument = +argument;
		return markFunction(function( seed, matches ) {
			var j,
				matchIndexes = fn( [], seed.length, argument ),
				i = matchIndexes.length;

			// Match elements found at the specified indexes
			while ( i-- ) {
				if ( seed[ (j = matchIndexes[i]) ] ) {
					seed[j] = !(matches[j] = seed[j]);
				}
			}
		});
	});
}

/**
 * Checks a node for validity as a Sizzle context
 * @param {Element|Object=} context
 * @returns {Element|Object|Boolean} The input node if acceptable, otherwise a falsy value
 */
function testContext( context ) {
	return context && typeof context.getElementsByTagName !== "undefined" && context;
}

// Expose support vars for convenience
support = Sizzle.support = {};

/**
 * Detects XML nodes
 * @param {Element|Object} elem An element or a document
 * @returns {Boolean} True iff elem is a non-HTML XML node
 */
isXML = Sizzle.isXML = function( elem ) {
	// documentElement is verified for cases where it doesn't yet exist
	// (such as loading iframes in IE - #4833)
	var documentElement = elem && (elem.ownerDocument || elem).documentElement;
	return documentElement ? documentElement.nodeName !== "HTML" : false;
};

/**
 * Sets document-related variables once based on the current document
 * @param {Element|Object} [doc] An element or document object to use to set the document
 * @returns {Object} Returns the current document
 */
setDocument = Sizzle.setDocument = function( node ) {
	var hasCompare, subWindow,
		doc = node ? node.ownerDocument || node : preferredDoc;

	// Return early if doc is invalid or already selected
	if ( doc === document || doc.nodeType !== 9 || !doc.documentElement ) {
		return document;
	}

	// Update global variables
	document = doc;
	docElem = document.documentElement;
	documentIsHTML = !isXML( document );

	// Support: IE 9-11, Edge
	// Accessing iframe documents after unload throws "permission denied" errors (jQuery #13936)
	if ( preferredDoc !== document &&
		(subWindow = document.defaultView) && subWindow.top !== subWindow ) {

		// Support: IE 11, Edge
		if ( subWindow.addEventListener ) {
			subWindow.addEventListener( "unload", unloadHandler, false );

		// Support: IE 9 - 10 only
		} else if ( subWindow.attachEvent ) {
			subWindow.attachEvent( "onunload", unloadHandler );
		}
	}

	/* Attributes
	---------------------------------------------------------------------- */

	// Support: IE<8
	// Verify that getAttribute really returns attributes and not properties
	// (excepting IE8 booleans)
	support.attributes = assert(function( el ) {
		el.className = "i";
		return !el.getAttribute("className");
	});

	/* getElement(s)By*
	---------------------------------------------------------------------- */

	// Check if getElementsByTagName("*") returns only elements
	support.getElementsByTagName = assert(function( el ) {
		el.appendChild( document.createComment("") );
		return !el.getElementsByTagName("*").length;
	});

	// Support: IE<9
	support.getElementsByClassName = rnative.test( document.getElementsByClassName );

	// Support: IE<10
	// Check if getElementById returns elements by name
	// The broken getElementById methods don't pick up programmatically-set names,
	// so use a roundabout getElementsByName test
	support.getById = assert(function( el ) {
		docElem.appendChild( el ).id = expando;
		return !document.getElementsByName || !document.getElementsByName( expando ).length;
	});

	// ID filter and find
	if ( support.getById ) {
		Expr.filter["ID"] = function( id ) {
			var attrId = id.replace( runescape, funescape );
			return function( elem ) {
				return elem.getAttribute("id") === attrId;
			};
		};
		Expr.find["ID"] = function( id, context ) {
			if ( typeof context.getElementById !== "undefined" && documentIsHTML ) {
				var elem = context.getElementById( id );
				return elem ? [ elem ] : [];
			}
		};
	} else {
		Expr.filter["ID"] =  function( id ) {
			var attrId = id.replace( runescape, funescape );
			return function( elem ) {
				var node = typeof elem.getAttributeNode !== "undefined" &&
					elem.getAttributeNode("id");
				return node && node.value === attrId;
			};
		};

		// Support: IE 6 - 7 only
		// getElementById is not reliable as a find shortcut
		Expr.find["ID"] = function( id, context ) {
			if ( typeof context.getElementById !== "undefined" && documentIsHTML ) {
				var node, i, elems,
					elem = context.getElementById( id );

				if ( elem ) {

					// Verify the id attribute
					node = elem.getAttributeNode("id");
					if ( node && node.value === id ) {
						return [ elem ];
					}

					// Fall back on getElementsByName
					elems = context.getElementsByName( id );
					i = 0;
					while ( (elem = elems[i++]) ) {
						node = elem.getAttributeNode("id");
						if ( node && node.value === id ) {
							return [ elem ];
						}
					}
				}

				return [];
			}
		};
	}

	// Tag
	Expr.find["TAG"] = support.getElementsByTagName ?
		function( tag, context ) {
			if ( typeof context.getElementsByTagName !== "undefined" ) {
				return context.getElementsByTagName( tag );

			// DocumentFragment nodes don't have gEBTN
			} else if ( support.qsa ) {
				return context.querySelectorAll( tag );
			}
		} :

		function( tag, context ) {
			var elem,
				tmp = [],
				i = 0,
				// By happy coincidence, a (broken) gEBTN appears on DocumentFragment nodes too
				results = context.getElementsByTagName( tag );

			// Filter out possible comments
			if ( tag === "*" ) {
				while ( (elem = results[i++]) ) {
					if ( elem.nodeType === 1 ) {
						tmp.push( elem );
					}
				}

				return tmp;
			}
			return results;
		};

	// Class
	Expr.find["CLASS"] = support.getElementsByClassName && function( className, context ) {
		if ( typeof context.getElementsByClassName !== "undefined" && documentIsHTML ) {
			return context.getElementsByClassName( className );
		}
	};

	/* QSA/matchesSelector
	---------------------------------------------------------------------- */

	// QSA and matchesSelector support

	// matchesSelector(:active) reports false when true (IE9/Opera 11.5)
	rbuggyMatches = [];

	// qSa(:focus) reports false when true (Chrome 21)
	// We allow this because of a bug in IE8/9 that throws an error
	// whenever `document.activeElement` is accessed on an iframe
	// So, we allow :focus to pass through QSA all the time to avoid the IE error
	// See https://bugs.jquery.com/ticket/13378
	rbuggyQSA = [];

	if ( (support.qsa = rnative.test( document.querySelectorAll )) ) {
		// Build QSA regex
		// Regex strategy adopted from Diego Perini
		assert(function( el ) {
			// Select is set to empty string on purpose
			// This is to test IE's treatment of not explicitly
			// setting a boolean content attribute,
			// since its presence should be enough
			// https://bugs.jquery.com/ticket/12359
			docElem.appendChild( el ).innerHTML = "<a id='" + expando + "'></a>" +
				"<select id='" + expando + "-\r\\' msallowcapture=''>" +
				"<option selected=''></option></select>";

			// Support: IE8, Opera 11-12.16
			// Nothing should be selected when empty strings follow ^= or $= or *=
			// The test attribute must be unknown in Opera but "safe" for WinRT
			// https://msdn.microsoft.com/en-us/library/ie/hh465388.aspx#attribute_section
			if ( el.querySelectorAll("[msallowcapture^='']").length ) {
				rbuggyQSA.push( "[*^$]=" + whitespace + "*(?:''|\"\")" );
			}

			// Support: IE8
			// Boolean attributes and "value" are not treated correctly
			if ( !el.querySelectorAll("[selected]").length ) {
				rbuggyQSA.push( "\\[" + whitespace + "*(?:value|" + booleans + ")" );
			}

			// Support: Chrome<29, Android<4.4, Safari<7.0+, iOS<7.0+, PhantomJS<1.9.8+
			if ( !el.querySelectorAll( "[id~=" + expando + "-]" ).length ) {
				rbuggyQSA.push("~=");
			}

			// Webkit/Opera - :checked should return selected option elements
			// http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
			// IE8 throws error here and will not see later tests
			if ( !el.querySelectorAll(":checked").length ) {
				rbuggyQSA.push(":checked");
			}

			// Support: Safari 8+, iOS 8+
			// https://bugs.webkit.org/show_bug.cgi?id=136851
			// In-page `selector#id sibling-combinator selector` fails
			if ( !el.querySelectorAll( "a#" + expando + "+*" ).length ) {
				rbuggyQSA.push(".#.+[+~]");
			}
		});

		assert(function( el ) {
			el.innerHTML = "<a href='' disabled='disabled'></a>" +
				"<select disabled='disabled'><option/></select>";

			// Support: Windows 8 Native Apps
			// The type and name attributes are restricted during .innerHTML assignment
			var input = document.createElement("input");
			input.setAttribute( "type", "hidden" );
			el.appendChild( input ).setAttribute( "name", "D" );

			// Support: IE8
			// Enforce case-sensitivity of name attribute
			if ( el.querySelectorAll("[name=d]").length ) {
				rbuggyQSA.push( "name" + whitespace + "*[*^$|!~]?=" );
			}

			// FF 3.5 - :enabled/:disabled and hidden elements (hidden elements are still enabled)
			// IE8 throws error here and will not see later tests
			if ( el.querySelectorAll(":enabled").length !== 2 ) {
				rbuggyQSA.push( ":enabled", ":disabled" );
			}

			// Support: IE9-11+
			// IE's :disabled selector does not pick up the children of disabled fieldsets
			docElem.appendChild( el ).disabled = true;
			if ( el.querySelectorAll(":disabled").length !== 2 ) {
				rbuggyQSA.push( ":enabled", ":disabled" );
			}

			// Opera 10-11 does not throw on post-comma invalid pseudos
			el.querySelectorAll("*,:x");
			rbuggyQSA.push(",.*:");
		});
	}

	if ( (support.matchesSelector = rnative.test( (matches = docElem.matches ||
		docElem.webkitMatchesSelector ||
		docElem.mozMatchesSelector ||
		docElem.oMatchesSelector ||
		docElem.msMatchesSelector) )) ) {

		assert(function( el ) {
			// Check to see if it's possible to do matchesSelector
			// on a disconnected node (IE 9)
			support.disconnectedMatch = matches.call( el, "*" );

			// This should fail with an exception
			// Gecko does not error, returns false instead
			matches.call( el, "[s!='']:x" );
			rbuggyMatches.push( "!=", pseudos );
		});
	}

	rbuggyQSA = rbuggyQSA.length && new RegExp( rbuggyQSA.join("|") );
	rbuggyMatches = rbuggyMatches.length && new RegExp( rbuggyMatches.join("|") );

	/* Contains
	---------------------------------------------------------------------- */
	hasCompare = rnative.test( docElem.compareDocumentPosition );

	// Element contains another
	// Purposefully self-exclusive
	// As in, an element does not contain itself
	contains = hasCompare || rnative.test( docElem.contains ) ?
		function( a, b ) {
			var adown = a.nodeType === 9 ? a.documentElement : a,
				bup = b && b.parentNode;
			return a === bup || !!( bup && bup.nodeType === 1 && (
				adown.contains ?
					adown.contains( bup ) :
					a.compareDocumentPosition && a.compareDocumentPosition( bup ) & 16
			));
		} :
		function( a, b ) {
			if ( b ) {
				while ( (b = b.parentNode) ) {
					if ( b === a ) {
						return true;
					}
				}
			}
			return false;
		};

	/* Sorting
	---------------------------------------------------------------------- */

	// Document order sorting
	sortOrder = hasCompare ?
	function( a, b ) {

		// Flag for duplicate removal
		if ( a === b ) {
			hasDuplicate = true;
			return 0;
		}

		// Sort on method existence if only one input has compareDocumentPosition
		var compare = !a.compareDocumentPosition - !b.compareDocumentPosition;
		if ( compare ) {
			return compare;
		}

		// Calculate position if both inputs belong to the same document
		compare = ( a.ownerDocument || a ) === ( b.ownerDocument || b ) ?
			a.compareDocumentPosition( b ) :

			// Otherwise we know they are disconnected
			1;

		// Disconnected nodes
		if ( compare & 1 ||
			(!support.sortDetached && b.compareDocumentPosition( a ) === compare) ) {

			// Choose the first element that is related to our preferred document
			if ( a === document || a.ownerDocument === preferredDoc && contains(preferredDoc, a) ) {
				return -1;
			}
			if ( b === document || b.ownerDocument === preferredDoc && contains(preferredDoc, b) ) {
				return 1;
			}

			// Maintain original order
			return sortInput ?
				( indexOf( sortInput, a ) - indexOf( sortInput, b ) ) :
				0;
		}

		return compare & 4 ? -1 : 1;
	} :
	function( a, b ) {
		// Exit early if the nodes are identical
		if ( a === b ) {
			hasDuplicate = true;
			return 0;
		}

		var cur,
			i = 0,
			aup = a.parentNode,
			bup = b.parentNode,
			ap = [ a ],
			bp = [ b ];

		// Parentless nodes are either documents or disconnected
		if ( !aup || !bup ) {
			return a === document ? -1 :
				b === document ? 1 :
				aup ? -1 :
				bup ? 1 :
				sortInput ?
				( indexOf( sortInput, a ) - indexOf( sortInput, b ) ) :
				0;

		// If the nodes are siblings, we can do a quick check
		} else if ( aup === bup ) {
			return siblingCheck( a, b );
		}

		// Otherwise we need full lists of their ancestors for comparison
		cur = a;
		while ( (cur = cur.parentNode) ) {
			ap.unshift( cur );
		}
		cur = b;
		while ( (cur = cur.parentNode) ) {
			bp.unshift( cur );
		}

		// Walk down the tree looking for a discrepancy
		while ( ap[i] === bp[i] ) {
			i++;
		}

		return i ?
			// Do a sibling check if the nodes have a common ancestor
			siblingCheck( ap[i], bp[i] ) :

			// Otherwise nodes in our document sort first
			ap[i] === preferredDoc ? -1 :
			bp[i] === preferredDoc ? 1 :
			0;
	};

	return document;
};

Sizzle.matches = function( expr, elements ) {
	return Sizzle( expr, null, null, elements );
};

Sizzle.matchesSelector = function( elem, expr ) {
	// Set document vars if needed
	if ( ( elem.ownerDocument || elem ) !== document ) {
		setDocument( elem );
	}

	// Make sure that attribute selectors are quoted
	expr = expr.replace( rattributeQuotes, "='$1']" );

	if ( support.matchesSelector && documentIsHTML &&
		!compilerCache[ expr + " " ] &&
		( !rbuggyMatches || !rbuggyMatches.test( expr ) ) &&
		( !rbuggyQSA     || !rbuggyQSA.test( expr ) ) ) {

		try {
			var ret = matches.call( elem, expr );

			// IE 9's matchesSelector returns false on disconnected nodes
			if ( ret || support.disconnectedMatch ||
					// As well, disconnected nodes are said to be in a document
					// fragment in IE 9
					elem.document && elem.document.nodeType !== 11 ) {
				return ret;
			}
		} catch (e) {}
	}

	return Sizzle( expr, document, null, [ elem ] ).length > 0;
};

Sizzle.contains = function( context, elem ) {
	// Set document vars if needed
	if ( ( context.ownerDocument || context ) !== document ) {
		setDocument( context );
	}
	return contains( context, elem );
};

Sizzle.attr = function( elem, name ) {
	// Set document vars if needed
	if ( ( elem.ownerDocument || elem ) !== document ) {
		setDocument( elem );
	}

	var fn = Expr.attrHandle[ name.toLowerCase() ],
		// Don't get fooled by Object.prototype properties (jQuery #13807)
		val = fn && hasOwn.call( Expr.attrHandle, name.toLowerCase() ) ?
			fn( elem, name, !documentIsHTML ) :
			undefined;

	return val !== undefined ?
		val :
		support.attributes || !documentIsHTML ?
			elem.getAttribute( name ) :
			(val = elem.getAttributeNode(name)) && val.specified ?
				val.value :
				null;
};

Sizzle.escape = function( sel ) {
	return (sel + "").replace( rcssescape, fcssescape );
};

Sizzle.error = function( msg ) {
	throw new Error( "Syntax error, unrecognized expression: " + msg );
};

/**
 * Document sorting and removing duplicates
 * @param {ArrayLike} results
 */
Sizzle.uniqueSort = function( results ) {
	var elem,
		duplicates = [],
		j = 0,
		i = 0;

	// Unless we *know* we can detect duplicates, assume their presence
	hasDuplicate = !support.detectDuplicates;
	sortInput = !support.sortStable && results.slice( 0 );
	results.sort( sortOrder );

	if ( hasDuplicate ) {
		while ( (elem = results[i++]) ) {
			if ( elem === results[ i ] ) {
				j = duplicates.push( i );
			}
		}
		while ( j-- ) {
			results.splice( duplicates[ j ], 1 );
		}
	}

	// Clear input after sorting to release objects
	// See https://github.com/jquery/sizzle/pull/225
	sortInput = null;

	return results;
};

/**
 * Utility function for retrieving the text value of an array of DOM nodes
 * @param {Array|Element} elem
 */
getText = Sizzle.getText = function( elem ) {
	var node,
		ret = "",
		i = 0,
		nodeType = elem.nodeType;

	if ( !nodeType ) {
		// If no nodeType, this is expected to be an array
		while ( (node = elem[i++]) ) {
			// Do not traverse comment nodes
			ret += getText( node );
		}
	} else if ( nodeType === 1 || nodeType === 9 || nodeType === 11 ) {
		// Use textContent for elements
		// innerText usage removed for consistency of new lines (jQuery #11153)
		if ( typeof elem.textContent === "string" ) {
			return elem.textContent;
		} else {
			// Traverse its children
			for ( elem = elem.firstChild; elem; elem = elem.nextSibling ) {
				ret += getText( elem );
			}
		}
	} else if ( nodeType === 3 || nodeType === 4 ) {
		return elem.nodeValue;
	}
	// Do not include comment or processing instruction nodes

	return ret;
};

Expr = Sizzle.selectors = {

	// Can be adjusted by the user
	cacheLength: 50,

	createPseudo: markFunction,

	match: matchExpr,

	attrHandle: {},

	find: {},

	relative: {
		">": { dir: "parentNode", first: true },
		" ": { dir: "parentNode" },
		"+": { dir: "previousSibling", first: true },
		"~": { dir: "previousSibling" }
	},

	preFilter: {
		"ATTR": function( match ) {
			match[1] = match[1].replace( runescape, funescape );

			// Move the given value to match[3] whether quoted or unquoted
			match[3] = ( match[3] || match[4] || match[5] || "" ).replace( runescape, funescape );

			if ( match[2] === "~=" ) {
				match[3] = " " + match[3] + " ";
			}

			return match.slice( 0, 4 );
		},

		"CHILD": function( match ) {
			/* matches from matchExpr["CHILD"]
				1 type (only|nth|...)
				2 what (child|of-type)
				3 argument (even|odd|\d*|\d*n([+-]\d+)?|...)
				4 xn-component of xn+y argument ([+-]?\d*n|)
				5 sign of xn-component
				6 x of xn-component
				7 sign of y-component
				8 y of y-component
			*/
			match[1] = match[1].toLowerCase();

			if ( match[1].slice( 0, 3 ) === "nth" ) {
				// nth-* requires argument
				if ( !match[3] ) {
					Sizzle.error( match[0] );
				}

				// numeric x and y parameters for Expr.filter.CHILD
				// remember that false/true cast respectively to 0/1
				match[4] = +( match[4] ? match[5] + (match[6] || 1) : 2 * ( match[3] === "even" || match[3] === "odd" ) );
				match[5] = +( ( match[7] + match[8] ) || match[3] === "odd" );

			// other types prohibit arguments
			} else if ( match[3] ) {
				Sizzle.error( match[0] );
			}

			return match;
		},

		"PSEUDO": function( match ) {
			var excess,
				unquoted = !match[6] && match[2];

			if ( matchExpr["CHILD"].test( match[0] ) ) {
				return null;
			}

			// Accept quoted arguments as-is
			if ( match[3] ) {
				match[2] = match[4] || match[5] || "";

			// Strip excess characters from unquoted arguments
			} else if ( unquoted && rpseudo.test( unquoted ) &&
				// Get excess from tokenize (recursively)
				(excess = tokenize( unquoted, true )) &&
				// advance to the next closing parenthesis
				(excess = unquoted.indexOf( ")", unquoted.length - excess ) - unquoted.length) ) {

				// excess is a negative index
				match[0] = match[0].slice( 0, excess );
				match[2] = unquoted.slice( 0, excess );
			}

			// Return only captures needed by the pseudo filter method (type and argument)
			return match.slice( 0, 3 );
		}
	},

	filter: {

		"TAG": function( nodeNameSelector ) {
			var nodeName = nodeNameSelector.replace( runescape, funescape ).toLowerCase();
			return nodeNameSelector === "*" ?
				function() { return true; } :
				function( elem ) {
					return elem.nodeName && elem.nodeName.toLowerCase() === nodeName;
				};
		},

		"CLASS": function( className ) {
			var pattern = classCache[ className + " " ];

			return pattern ||
				(pattern = new RegExp( "(^|" + whitespace + ")" + className + "(" + whitespace + "|$)" )) &&
				classCache( className, function( elem ) {
					return pattern.test( typeof elem.className === "string" && elem.className || typeof elem.getAttribute !== "undefined" && elem.getAttribute("class") || "" );
				});
		},

		"ATTR": function( name, operator, check ) {
			return function( elem ) {
				var result = Sizzle.attr( elem, name );

				if ( result == null ) {
					return operator === "!=";
				}
				if ( !operator ) {
					return true;
				}

				result += "";

				return operator === "=" ? result === check :
					operator === "!=" ? result !== check :
					operator === "^=" ? check && result.indexOf( check ) === 0 :
					operator === "*=" ? check && result.indexOf( check ) > -1 :
					operator === "$=" ? check && result.slice( -check.length ) === check :
					operator === "~=" ? ( " " + result.replace( rwhitespace, " " ) + " " ).indexOf( check ) > -1 :
					operator === "|=" ? result === check || result.slice( 0, check.length + 1 ) === check + "-" :
					false;
			};
		},

		"CHILD": function( type, what, argument, first, last ) {
			var simple = type.slice( 0, 3 ) !== "nth",
				forward = type.slice( -4 ) !== "last",
				ofType = what === "of-type";

			return first === 1 && last === 0 ?

				// Shortcut for :nth-*(n)
				function( elem ) {
					return !!elem.parentNode;
				} :

				function( elem, context, xml ) {
					var cache, uniqueCache, outerCache, node, nodeIndex, start,
						dir = simple !== forward ? "nextSibling" : "previousSibling",
						parent = elem.parentNode,
						name = ofType && elem.nodeName.toLowerCase(),
						useCache = !xml && !ofType,
						diff = false;

					if ( parent ) {

						// :(first|last|only)-(child|of-type)
						if ( simple ) {
							while ( dir ) {
								node = elem;
								while ( (node = node[ dir ]) ) {
									if ( ofType ?
										node.nodeName.toLowerCase() === name :
										node.nodeType === 1 ) {

										return false;
									}
								}
								// Reverse direction for :only-* (if we haven't yet done so)
								start = dir = type === "only" && !start && "nextSibling";
							}
							return true;
						}

						start = [ forward ? parent.firstChild : parent.lastChild ];

						// non-xml :nth-child(...) stores cache data on `parent`
						if ( forward && useCache ) {

							// Seek `elem` from a previously-cached index

							// ...in a gzip-friendly way
							node = parent;
							outerCache = node[ expando ] || (node[ expando ] = {});

							// Support: IE <9 only
							// Defend against cloned attroperties (jQuery gh-1709)
							uniqueCache = outerCache[ node.uniqueID ] ||
								(outerCache[ node.uniqueID ] = {});

							cache = uniqueCache[ type ] || [];
							nodeIndex = cache[ 0 ] === dirruns && cache[ 1 ];
							diff = nodeIndex && cache[ 2 ];
							node = nodeIndex && parent.childNodes[ nodeIndex ];

							while ( (node = ++nodeIndex && node && node[ dir ] ||

								// Fallback to seeking `elem` from the start
								(diff = nodeIndex = 0) || start.pop()) ) {

								// When found, cache indexes on `parent` and break
								if ( node.nodeType === 1 && ++diff && node === elem ) {
									uniqueCache[ type ] = [ dirruns, nodeIndex, diff ];
									break;
								}
							}

						} else {
							// Use previously-cached element index if available
							if ( useCache ) {
								// ...in a gzip-friendly way
								node = elem;
								outerCache = node[ expando ] || (node[ expando ] = {});

								// Support: IE <9 only
								// Defend against cloned attroperties (jQuery gh-1709)
								uniqueCache = outerCache[ node.uniqueID ] ||
									(outerCache[ node.uniqueID ] = {});

								cache = uniqueCache[ type ] || [];
								nodeIndex = cache[ 0 ] === dirruns && cache[ 1 ];
								diff = nodeIndex;
							}

							// xml :nth-child(...)
							// or :nth-last-child(...) or :nth(-last)?-of-type(...)
							if ( diff === false ) {
								// Use the same loop as above to seek `elem` from the start
								while ( (node = ++nodeIndex && node && node[ dir ] ||
									(diff = nodeIndex = 0) || start.pop()) ) {

									if ( ( ofType ?
										node.nodeName.toLowerCase() === name :
										node.nodeType === 1 ) &&
										++diff ) {

										// Cache the index of each encountered element
										if ( useCache ) {
											outerCache = node[ expando ] || (node[ expando ] = {});

											// Support: IE <9 only
											// Defend against cloned attroperties (jQuery gh-1709)
											uniqueCache = outerCache[ node.uniqueID ] ||
												(outerCache[ node.uniqueID ] = {});

											uniqueCache[ type ] = [ dirruns, diff ];
										}

										if ( node === elem ) {
											break;
										}
									}
								}
							}
						}

						// Incorporate the offset, then check against cycle size
						diff -= last;
						return diff === first || ( diff % first === 0 && diff / first >= 0 );
					}
				};
		},

		"PSEUDO": function( pseudo, argument ) {
			// pseudo-class names are case-insensitive
			// http://www.w3.org/TR/selectors/#pseudo-classes
			// Prioritize by case sensitivity in case custom pseudos are added with uppercase letters
			// Remember that setFilters inherits from pseudos
			var args,
				fn = Expr.pseudos[ pseudo ] || Expr.setFilters[ pseudo.toLowerCase() ] ||
					Sizzle.error( "unsupported pseudo: " + pseudo );

			// The user may use createPseudo to indicate that
			// arguments are needed to create the filter function
			// just as Sizzle does
			if ( fn[ expando ] ) {
				return fn( argument );
			}

			// But maintain support for old signatures
			if ( fn.length > 1 ) {
				args = [ pseudo, pseudo, "", argument ];
				return Expr.setFilters.hasOwnProperty( pseudo.toLowerCase() ) ?
					markFunction(function( seed, matches ) {
						var idx,
							matched = fn( seed, argument ),
							i = matched.length;
						while ( i-- ) {
							idx = indexOf( seed, matched[i] );
							seed[ idx ] = !( matches[ idx ] = matched[i] );
						}
					}) :
					function( elem ) {
						return fn( elem, 0, args );
					};
			}

			return fn;
		}
	},

	pseudos: {
		// Potentially complex pseudos
		"not": markFunction(function( selector ) {
			// Trim the selector passed to compile
			// to avoid treating leading and trailing
			// spaces as combinators
			var input = [],
				results = [],
				matcher = compile( selector.replace( rtrim, "$1" ) );

			return matcher[ expando ] ?
				markFunction(function( seed, matches, context, xml ) {
					var elem,
						unmatched = matcher( seed, null, xml, [] ),
						i = seed.length;

					// Match elements unmatched by `matcher`
					while ( i-- ) {
						if ( (elem = unmatched[i]) ) {
							seed[i] = !(matches[i] = elem);
						}
					}
				}) :
				function( elem, context, xml ) {
					input[0] = elem;
					matcher( input, null, xml, results );
					// Don't keep the element (issue #299)
					input[0] = null;
					return !results.pop();
				};
		}),

		"has": markFunction(function( selector ) {
			return function( elem ) {
				return Sizzle( selector, elem ).length > 0;
			};
		}),

		"contains": markFunction(function( text ) {
			text = text.replace( runescape, funescape );
			return function( elem ) {
				return ( elem.textContent || elem.innerText || getText( elem ) ).indexOf( text ) > -1;
			};
		}),

		// "Whether an element is represented by a :lang() selector
		// is based solely on the element's language value
		// being equal to the identifier C,
		// or beginning with the identifier C immediately followed by "-".
		// The matching of C against the element's language value is performed case-insensitively.
		// The identifier C does not have to be a valid language name."
		// http://www.w3.org/TR/selectors/#lang-pseudo
		"lang": markFunction( function( lang ) {
			// lang value must be a valid identifier
			if ( !ridentifier.test(lang || "") ) {
				Sizzle.error( "unsupported lang: " + lang );
			}
			lang = lang.replace( runescape, funescape ).toLowerCase();
			return function( elem ) {
				var elemLang;
				do {
					if ( (elemLang = documentIsHTML ?
						elem.lang :
						elem.getAttribute("xml:lang") || elem.getAttribute("lang")) ) {

						elemLang = elemLang.toLowerCase();
						return elemLang === lang || elemLang.indexOf( lang + "-" ) === 0;
					}
				} while ( (elem = elem.parentNode) && elem.nodeType === 1 );
				return false;
			};
		}),

		// Miscellaneous
		"target": function( elem ) {
			var hash = window.location && window.location.hash;
			return hash && hash.slice( 1 ) === elem.id;
		},

		"root": function( elem ) {
			return elem === docElem;
		},

		"focus": function( elem ) {
			return elem === document.activeElement && (!document.hasFocus || document.hasFocus()) && !!(elem.type || elem.href || ~elem.tabIndex);
		},

		// Boolean properties
		"enabled": createDisabledPseudo( false ),
		"disabled": createDisabledPseudo( true ),

		"checked": function( elem ) {
			// In CSS3, :checked should return both checked and selected elements
			// http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
			var nodeName = elem.nodeName.toLowerCase();
			return (nodeName === "input" && !!elem.checked) || (nodeName === "option" && !!elem.selected);
		},

		"selected": function( elem ) {
			// Accessing this property makes selected-by-default
			// options in Safari work properly
			if ( elem.parentNode ) {
				elem.parentNode.selectedIndex;
			}

			return elem.selected === true;
		},

		// Contents
		"empty": function( elem ) {
			// http://www.w3.org/TR/selectors/#empty-pseudo
			// :empty is negated by element (1) or content nodes (text: 3; cdata: 4; entity ref: 5),
			//   but not by others (comment: 8; processing instruction: 7; etc.)
			// nodeType < 6 works because attributes (2) do not appear as children
			for ( elem = elem.firstChild; elem; elem = elem.nextSibling ) {
				if ( elem.nodeType < 6 ) {
					return false;
				}
			}
			return true;
		},

		"parent": function( elem ) {
			return !Expr.pseudos["empty"]( elem );
		},

		// Element/input types
		"header": function( elem ) {
			return rheader.test( elem.nodeName );
		},

		"input": function( elem ) {
			return rinputs.test( elem.nodeName );
		},

		"button": function( elem ) {
			var name = elem.nodeName.toLowerCase();
			return name === "input" && elem.type === "button" || name === "button";
		},

		"text": function( elem ) {
			var attr;
			return elem.nodeName.toLowerCase() === "input" &&
				elem.type === "text" &&

				// Support: IE<8
				// New HTML5 attribute values (e.g., "search") appear with elem.type === "text"
				( (attr = elem.getAttribute("type")) == null || attr.toLowerCase() === "text" );
		},

		// Position-in-collection
		"first": createPositionalPseudo(function() {
			return [ 0 ];
		}),

		"last": createPositionalPseudo(function( matchIndexes, length ) {
			return [ length - 1 ];
		}),

		"eq": createPositionalPseudo(function( matchIndexes, length, argument ) {
			return [ argument < 0 ? argument + length : argument ];
		}),

		"even": createPositionalPseudo(function( matchIndexes, length ) {
			var i = 0;
			for ( ; i < length; i += 2 ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		}),

		"odd": createPositionalPseudo(function( matchIndexes, length ) {
			var i = 1;
			for ( ; i < length; i += 2 ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		}),

		"lt": createPositionalPseudo(function( matchIndexes, length, argument ) {
			var i = argument < 0 ? argument + length : argument;
			for ( ; --i >= 0; ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		}),

		"gt": createPositionalPseudo(function( matchIndexes, length, argument ) {
			var i = argument < 0 ? argument + length : argument;
			for ( ; ++i < length; ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		})
	}
};

Expr.pseudos["nth"] = Expr.pseudos["eq"];

// Add button/input type pseudos
for ( i in { radio: true, checkbox: true, file: true, password: true, image: true } ) {
	Expr.pseudos[ i ] = createInputPseudo( i );
}
for ( i in { submit: true, reset: true } ) {
	Expr.pseudos[ i ] = createButtonPseudo( i );
}

// Easy API for creating new setFilters
function setFilters() {}
setFilters.prototype = Expr.filters = Expr.pseudos;
Expr.setFilters = new setFilters();

tokenize = Sizzle.tokenize = function( selector, parseOnly ) {
	var matched, match, tokens, type,
		soFar, groups, preFilters,
		cached = tokenCache[ selector + " " ];

	if ( cached ) {
		return parseOnly ? 0 : cached.slice( 0 );
	}

	soFar = selector;
	groups = [];
	preFilters = Expr.preFilter;

	while ( soFar ) {

		// Comma and first run
		if ( !matched || (match = rcomma.exec( soFar )) ) {
			if ( match ) {
				// Don't consume trailing commas as valid
				soFar = soFar.slice( match[0].length ) || soFar;
			}
			groups.push( (tokens = []) );
		}

		matched = false;

		// Combinators
		if ( (match = rcombinators.exec( soFar )) ) {
			matched = match.shift();
			tokens.push({
				value: matched,
				// Cast descendant combinators to space
				type: match[0].replace( rtrim, " " )
			});
			soFar = soFar.slice( matched.length );
		}

		// Filters
		for ( type in Expr.filter ) {
			if ( (match = matchExpr[ type ].exec( soFar )) && (!preFilters[ type ] ||
				(match = preFilters[ type ]( match ))) ) {
				matched = match.shift();
				tokens.push({
					value: matched,
					type: type,
					matches: match
				});
				soFar = soFar.slice( matched.length );
			}
		}

		if ( !matched ) {
			break;
		}
	}

	// Return the length of the invalid excess
	// if we're just parsing
	// Otherwise, throw an error or return tokens
	return parseOnly ?
		soFar.length :
		soFar ?
			Sizzle.error( selector ) :
			// Cache the tokens
			tokenCache( selector, groups ).slice( 0 );
};

function toSelector( tokens ) {
	var i = 0,
		len = tokens.length,
		selector = "";
	for ( ; i < len; i++ ) {
		selector += tokens[i].value;
	}
	return selector;
}

function addCombinator( matcher, combinator, base ) {
	var dir = combinator.dir,
		skip = combinator.next,
		key = skip || dir,
		checkNonElements = base && key === "parentNode",
		doneName = done++;

	return combinator.first ?
		// Check against closest ancestor/preceding element
		function( elem, context, xml ) {
			while ( (elem = elem[ dir ]) ) {
				if ( elem.nodeType === 1 || checkNonElements ) {
					return matcher( elem, context, xml );
				}
			}
			return false;
		} :

		// Check against all ancestor/preceding elements
		function( elem, context, xml ) {
			var oldCache, uniqueCache, outerCache,
				newCache = [ dirruns, doneName ];

			// We can't set arbitrary data on XML nodes, so they don't benefit from combinator caching
			if ( xml ) {
				while ( (elem = elem[ dir ]) ) {
					if ( elem.nodeType === 1 || checkNonElements ) {
						if ( matcher( elem, context, xml ) ) {
							return true;
						}
					}
				}
			} else {
				while ( (elem = elem[ dir ]) ) {
					if ( elem.nodeType === 1 || checkNonElements ) {
						outerCache = elem[ expando ] || (elem[ expando ] = {});

						// Support: IE <9 only
						// Defend against cloned attroperties (jQuery gh-1709)
						uniqueCache = outerCache[ elem.uniqueID ] || (outerCache[ elem.uniqueID ] = {});

						if ( skip && skip === elem.nodeName.toLowerCase() ) {
							elem = elem[ dir ] || elem;
						} else if ( (oldCache = uniqueCache[ key ]) &&
							oldCache[ 0 ] === dirruns && oldCache[ 1 ] === doneName ) {

							// Assign to newCache so results back-propagate to previous elements
							return (newCache[ 2 ] = oldCache[ 2 ]);
						} else {
							// Reuse newcache so results back-propagate to previous elements
							uniqueCache[ key ] = newCache;

							// A match means we're done; a fail means we have to keep checking
							if ( (newCache[ 2 ] = matcher( elem, context, xml )) ) {
								return true;
							}
						}
					}
				}
			}
			return false;
		};
}

function elementMatcher( matchers ) {
	return matchers.length > 1 ?
		function( elem, context, xml ) {
			var i = matchers.length;
			while ( i-- ) {
				if ( !matchers[i]( elem, context, xml ) ) {
					return false;
				}
			}
			return true;
		} :
		matchers[0];
}

function multipleContexts( selector, contexts, results ) {
	var i = 0,
		len = contexts.length;
	for ( ; i < len; i++ ) {
		Sizzle( selector, contexts[i], results );
	}
	return results;
}

function condense( unmatched, map, filter, context, xml ) {
	var elem,
		newUnmatched = [],
		i = 0,
		len = unmatched.length,
		mapped = map != null;

	for ( ; i < len; i++ ) {
		if ( (elem = unmatched[i]) ) {
			if ( !filter || filter( elem, context, xml ) ) {
				newUnmatched.push( elem );
				if ( mapped ) {
					map.push( i );
				}
			}
		}
	}

	return newUnmatched;
}

function setMatcher( preFilter, selector, matcher, postFilter, postFinder, postSelector ) {
	if ( postFilter && !postFilter[ expando ] ) {
		postFilter = setMatcher( postFilter );
	}
	if ( postFinder && !postFinder[ expando ] ) {
		postFinder = setMatcher( postFinder, postSelector );
	}
	return markFunction(function( seed, results, context, xml ) {
		var temp, i, elem,
			preMap = [],
			postMap = [],
			preexisting = results.length,

			// Get initial elements from seed or context
			elems = seed || multipleContexts( selector || "*", context.nodeType ? [ context ] : context, [] ),

			// Prefilter to get matcher input, preserving a map for seed-results synchronization
			matcherIn = preFilter && ( seed || !selector ) ?
				condense( elems, preMap, preFilter, context, xml ) :
				elems,

			matcherOut = matcher ?
				// If we have a postFinder, or filtered seed, or non-seed postFilter or preexisting results,
				postFinder || ( seed ? preFilter : preexisting || postFilter ) ?

					// ...intermediate processing is necessary
					[] :

					// ...otherwise use results directly
					results :
				matcherIn;

		// Find primary matches
		if ( matcher ) {
			matcher( matcherIn, matcherOut, context, xml );
		}

		// Apply postFilter
		if ( postFilter ) {
			temp = condense( matcherOut, postMap );
			postFilter( temp, [], context, xml );

			// Un-match failing elements by moving them back to matcherIn
			i = temp.length;
			while ( i-- ) {
				if ( (elem = temp[i]) ) {
					matcherOut[ postMap[i] ] = !(matcherIn[ postMap[i] ] = elem);
				}
			}
		}

		if ( seed ) {
			if ( postFinder || preFilter ) {
				if ( postFinder ) {
					// Get the final matcherOut by condensing this intermediate into postFinder contexts
					temp = [];
					i = matcherOut.length;
					while ( i-- ) {
						if ( (elem = matcherOut[i]) ) {
							// Restore matcherIn since elem is not yet a final match
							temp.push( (matcherIn[i] = elem) );
						}
					}
					postFinder( null, (matcherOut = []), temp, xml );
				}

				// Move matched elements from seed to results to keep them synchronized
				i = matcherOut.length;
				while ( i-- ) {
					if ( (elem = matcherOut[i]) &&
						(temp = postFinder ? indexOf( seed, elem ) : preMap[i]) > -1 ) {

						seed[temp] = !(results[temp] = elem);
					}
				}
			}

		// Add elements to results, through postFinder if defined
		} else {
			matcherOut = condense(
				matcherOut === results ?
					matcherOut.splice( preexisting, matcherOut.length ) :
					matcherOut
			);
			if ( postFinder ) {
				postFinder( null, results, matcherOut, xml );
			} else {
				push.apply( results, matcherOut );
			}
		}
	});
}

function matcherFromTokens( tokens ) {
	var checkContext, matcher, j,
		len = tokens.length,
		leadingRelative = Expr.relative[ tokens[0].type ],
		implicitRelative = leadingRelative || Expr.relative[" "],
		i = leadingRelative ? 1 : 0,

		// The foundational matcher ensures that elements are reachable from top-level context(s)
		matchContext = addCombinator( function( elem ) {
			return elem === checkContext;
		}, implicitRelative, true ),
		matchAnyContext = addCombinator( function( elem ) {
			return indexOf( checkContext, elem ) > -1;
		}, implicitRelative, true ),
		matchers = [ function( elem, context, xml ) {
			var ret = ( !leadingRelative && ( xml || context !== outermostContext ) ) || (
				(checkContext = context).nodeType ?
					matchContext( elem, context, xml ) :
					matchAnyContext( elem, context, xml ) );
			// Avoid hanging onto element (issue #299)
			checkContext = null;
			return ret;
		} ];

	for ( ; i < len; i++ ) {
		if ( (matcher = Expr.relative[ tokens[i].type ]) ) {
			matchers = [ addCombinator(elementMatcher( matchers ), matcher) ];
		} else {
			matcher = Expr.filter[ tokens[i].type ].apply( null, tokens[i].matches );

			// Return special upon seeing a positional matcher
			if ( matcher[ expando ] ) {
				// Find the next relative operator (if any) for proper handling
				j = ++i;
				for ( ; j < len; j++ ) {
					if ( Expr.relative[ tokens[j].type ] ) {
						break;
					}
				}
				return setMatcher(
					i > 1 && elementMatcher( matchers ),
					i > 1 && toSelector(
						// If the preceding token was a descendant combinator, insert an implicit any-element `*`
						tokens.slice( 0, i - 1 ).concat({ value: tokens[ i - 2 ].type === " " ? "*" : "" })
					).replace( rtrim, "$1" ),
					matcher,
					i < j && matcherFromTokens( tokens.slice( i, j ) ),
					j < len && matcherFromTokens( (tokens = tokens.slice( j )) ),
					j < len && toSelector( tokens )
				);
			}
			matchers.push( matcher );
		}
	}

	return elementMatcher( matchers );
}

function matcherFromGroupMatchers( elementMatchers, setMatchers ) {
	var bySet = setMatchers.length > 0,
		byElement = elementMatchers.length > 0,
		superMatcher = function( seed, context, xml, results, outermost ) {
			var elem, j, matcher,
				matchedCount = 0,
				i = "0",
				unmatched = seed && [],
				setMatched = [],
				contextBackup = outermostContext,
				// We must always have either seed elements or outermost context
				elems = seed || byElement && Expr.find["TAG"]( "*", outermost ),
				// Use integer dirruns iff this is the outermost matcher
				dirrunsUnique = (dirruns += contextBackup == null ? 1 : Math.random() || 0.1),
				len = elems.length;

			if ( outermost ) {
				outermostContext = context === document || context || outermost;
			}

			// Add elements passing elementMatchers directly to results
			// Support: IE<9, Safari
			// Tolerate NodeList properties (IE: "length"; Safari: <number>) matching elements by id
			for ( ; i !== len && (elem = elems[i]) != null; i++ ) {
				if ( byElement && elem ) {
					j = 0;
					if ( !context && elem.ownerDocument !== document ) {
						setDocument( elem );
						xml = !documentIsHTML;
					}
					while ( (matcher = elementMatchers[j++]) ) {
						if ( matcher( elem, context || document, xml) ) {
							results.push( elem );
							break;
						}
					}
					if ( outermost ) {
						dirruns = dirrunsUnique;
					}
				}

				// Track unmatched elements for set filters
				if ( bySet ) {
					// They will have gone through all possible matchers
					if ( (elem = !matcher && elem) ) {
						matchedCount--;
					}

					// Lengthen the array for every element, matched or not
					if ( seed ) {
						unmatched.push( elem );
					}
				}
			}

			// `i` is now the count of elements visited above, and adding it to `matchedCount`
			// makes the latter nonnegative.
			matchedCount += i;

			// Apply set filters to unmatched elements
			// NOTE: This can be skipped if there are no unmatched elements (i.e., `matchedCount`
			// equals `i`), unless we didn't visit _any_ elements in the above loop because we have
			// no element matchers and no seed.
			// Incrementing an initially-string "0" `i` allows `i` to remain a string only in that
			// case, which will result in a "00" `matchedCount` that differs from `i` but is also
			// numerically zero.
			if ( bySet && i !== matchedCount ) {
				j = 0;
				while ( (matcher = setMatchers[j++]) ) {
					matcher( unmatched, setMatched, context, xml );
				}

				if ( seed ) {
					// Reintegrate element matches to eliminate the need for sorting
					if ( matchedCount > 0 ) {
						while ( i-- ) {
							if ( !(unmatched[i] || setMatched[i]) ) {
								setMatched[i] = pop.call( results );
							}
						}
					}

					// Discard index placeholder values to get only actual matches
					setMatched = condense( setMatched );
				}

				// Add matches to results
				push.apply( results, setMatched );

				// Seedless set matches succeeding multiple successful matchers stipulate sorting
				if ( outermost && !seed && setMatched.length > 0 &&
					( matchedCount + setMatchers.length ) > 1 ) {

					Sizzle.uniqueSort( results );
				}
			}

			// Override manipulation of globals by nested matchers
			if ( outermost ) {
				dirruns = dirrunsUnique;
				outermostContext = contextBackup;
			}

			return unmatched;
		};

	return bySet ?
		markFunction( superMatcher ) :
		superMatcher;
}

compile = Sizzle.compile = function( selector, match /* Internal Use Only */ ) {
	var i,
		setMatchers = [],
		elementMatchers = [],
		cached = compilerCache[ selector + " " ];

	if ( !cached ) {
		// Generate a function of recursive functions that can be used to check each element
		if ( !match ) {
			match = tokenize( selector );
		}
		i = match.length;
		while ( i-- ) {
			cached = matcherFromTokens( match[i] );
			if ( cached[ expando ] ) {
				setMatchers.push( cached );
			} else {
				elementMatchers.push( cached );
			}
		}

		// Cache the compiled function
		cached = compilerCache( selector, matcherFromGroupMatchers( elementMatchers, setMatchers ) );

		// Save selector and tokenization
		cached.selector = selector;
	}
	return cached;
};

/**
 * A low-level selection function that works with Sizzle's compiled
 *  selector functions
 * @param {String|Function} selector A selector or a pre-compiled
 *  selector function built with Sizzle.compile
 * @param {Element} context
 * @param {Array} [results]
 * @param {Array} [seed] A set of elements to match against
 */
select = Sizzle.select = function( selector, context, results, seed ) {
	var i, tokens, token, type, find,
		compiled = typeof selector === "function" && selector,
		match = !seed && tokenize( (selector = compiled.selector || selector) );

	results = results || [];

	// Try to minimize operations if there is only one selector in the list and no seed
	// (the latter of which guarantees us context)
	if ( match.length === 1 ) {

		// Reduce context if the leading compound selector is an ID
		tokens = match[0] = match[0].slice( 0 );
		if ( tokens.length > 2 && (token = tokens[0]).type === "ID" &&
				context.nodeType === 9 && documentIsHTML && Expr.relative[ tokens[1].type ] ) {

			context = ( Expr.find["ID"]( token.matches[0].replace(runescape, funescape), context ) || [] )[0];
			if ( !context ) {
				return results;

			// Precompiled matchers will still verify ancestry, so step up a level
			} else if ( compiled ) {
				context = context.parentNode;
			}

			selector = selector.slice( tokens.shift().value.length );
		}

		// Fetch a seed set for right-to-left matching
		i = matchExpr["needsContext"].test( selector ) ? 0 : tokens.length;
		while ( i-- ) {
			token = tokens[i];

			// Abort if we hit a combinator
			if ( Expr.relative[ (type = token.type) ] ) {
				break;
			}
			if ( (find = Expr.find[ type ]) ) {
				// Search, expanding context for leading sibling combinators
				if ( (seed = find(
					token.matches[0].replace( runescape, funescape ),
					rsibling.test( tokens[0].type ) && testContext( context.parentNode ) || context
				)) ) {

					// If seed is empty or no tokens remain, we can return early
					tokens.splice( i, 1 );
					selector = seed.length && toSelector( tokens );
					if ( !selector ) {
						push.apply( results, seed );
						return results;
					}

					break;
				}
			}
		}
	}

	// Compile and execute a filtering function if one is not provided
	// Provide `match` to avoid retokenization if we modified the selector above
	( compiled || compile( selector, match ) )(
		seed,
		context,
		!documentIsHTML,
		results,
		!context || rsibling.test( selector ) && testContext( context.parentNode ) || context
	);
	return results;
};

// One-time assignments

// Sort stability
support.sortStable = expando.split("").sort( sortOrder ).join("") === expando;

// Support: Chrome 14-35+
// Always assume duplicates if they aren't passed to the comparison function
support.detectDuplicates = !!hasDuplicate;

// Initialize against the default document
setDocument();

// Support: Webkit<537.32 - Safari 6.0.3/Chrome 25 (fixed in Chrome 27)
// Detached nodes confoundingly follow *each other*
support.sortDetached = assert(function( el ) {
	// Should return 1, but returns 4 (following)
	return el.compareDocumentPosition( document.createElement("fieldset") ) & 1;
});

// Support: IE<8
// Prevent attribute/property "interpolation"
// https://msdn.microsoft.com/en-us/library/ms536429%28VS.85%29.aspx
if ( !assert(function( el ) {
	el.innerHTML = "<a href='#'></a>";
	return el.firstChild.getAttribute("href") === "#" ;
}) ) {
	addHandle( "type|href|height|width", function( elem, name, isXML ) {
		if ( !isXML ) {
			return elem.getAttribute( name, name.toLowerCase() === "type" ? 1 : 2 );
		}
	});
}

// Support: IE<9
// Use defaultValue in place of getAttribute("value")
if ( !support.attributes || !assert(function( el ) {
	el.innerHTML = "<input/>";
	el.firstChild.setAttribute( "value", "" );
	return el.firstChild.getAttribute( "value" ) === "";
}) ) {
	addHandle( "value", function( elem, name, isXML ) {
		if ( !isXML && elem.nodeName.toLowerCase() === "input" ) {
			return elem.defaultValue;
		}
	});
}

// Support: IE<9
// Use getAttributeNode to fetch booleans when getAttribute lies
if ( !assert(function( el ) {
	return el.getAttribute("disabled") == null;
}) ) {
	addHandle( booleans, function( elem, name, isXML ) {
		var val;
		if ( !isXML ) {
			return elem[ name ] === true ? name.toLowerCase() :
					(val = elem.getAttributeNode( name )) && val.specified ?
					val.value :
				null;
		}
	});
}

return Sizzle;

})( window );



jQuery.find = Sizzle;
jQuery.expr = Sizzle.selectors;

// Deprecated
jQuery.expr[ ":" ] = jQuery.expr.pseudos;
jQuery.uniqueSort = jQuery.unique = Sizzle.uniqueSort;
jQuery.text = Sizzle.getText;
jQuery.isXMLDoc = Sizzle.isXML;
jQuery.contains = Sizzle.contains;
jQuery.escapeSelector = Sizzle.escape;




var dir = function( elem, dir, until ) {
	var matched = [],
		truncate = until !== undefined;

	while ( ( elem = elem[ dir ] ) && elem.nodeType !== 9 ) {
		if ( elem.nodeType === 1 ) {
			if ( truncate && jQuery( elem ).is( until ) ) {
				break;
			}
			matched.push( elem );
		}
	}
	return matched;
};


var siblings = function( n, elem ) {
	var matched = [];

	for ( ; n; n = n.nextSibling ) {
		if ( n.nodeType === 1 && n !== elem ) {
			matched.push( n );
		}
	}

	return matched;
};


var rneedsContext = jQuery.expr.match.needsContext;



function nodeName( elem, name ) {

  return elem.nodeName && elem.nodeName.toLowerCase() === name.toLowerCase();

};
var rsingleTag = ( /^<([a-z][^\/\0>:\x20\t\r\n\f]*)[\x20\t\r\n\f]*\/?>(?:<\/\1>|)$/i );



var risSimple = /^.[^:#\[\.,]*$/;

// Implement the identical functionality for filter and not
function winnow( elements, qualifier, not ) {
	if ( jQuery.isFunction( qualifier ) ) {
		return jQuery.grep( elements, function( elem, i ) {
			return !!qualifier.call( elem, i, elem ) !== not;
		} );
	}

	// Single element
	if ( qualifier.nodeType ) {
		return jQuery.grep( elements, function( elem ) {
			return ( elem === qualifier ) !== not;
		} );
	}

	// Arraylike of elements (jQuery, arguments, Array)
	if ( typeof qualifier !== "string" ) {
		return jQuery.grep( elements, function( elem ) {
			return ( indexOf.call( qualifier, elem ) > -1 ) !== not;
		} );
	}

	// Simple selector that can be filtered directly, removing non-Elements
	if ( risSimple.test( qualifier ) ) {
		return jQuery.filter( qualifier, elements, not );
	}

	// Complex selector, compare the two sets, removing non-Elements
	qualifier = jQuery.filter( qualifier, elements );
	return jQuery.grep( elements, function( elem ) {
		return ( indexOf.call( qualifier, elem ) > -1 ) !== not && elem.nodeType === 1;
	} );
}

jQuery.filter = function( expr, elems, not ) {
	var elem = elems[ 0 ];

	if ( not ) {
		expr = ":not(" + expr + ")";
	}

	if ( elems.length === 1 && elem.nodeType === 1 ) {
		return jQuery.find.matchesSelector( elem, expr ) ? [ elem ] : [];
	}

	return jQuery.find.matches( expr, jQuery.grep( elems, function( elem ) {
		return elem.nodeType === 1;
	} ) );
};

jQuery.fn.extend( {
	find: function( selector ) {
		var i, ret,
			len = this.length,
			self = this;

		if ( typeof selector !== "string" ) {
			return this.pushStack( jQuery( selector ).filter( function() {
				for ( i = 0; i < len; i++ ) {
					if ( jQuery.contains( self[ i ], this ) ) {
						return true;
					}
				}
			} ) );
		}

		ret = this.pushStack( [] );

		for ( i = 0; i < len; i++ ) {
			jQuery.find( selector, self[ i ], ret );
		}

		return len > 1 ? jQuery.uniqueSort( ret ) : ret;
	},
	filter: function( selector ) {
		return this.pushStack( winnow( this, selector || [], false ) );
	},
	not: function( selector ) {
		return this.pushStack( winnow( this, selector || [], true ) );
	},
	is: function( selector ) {
		return !!winnow(
			this,

			// If this is a positional/relative selector, check membership in the returned set
			// so $("p:first").is("p:last") won't return true for a doc with two "p".
			typeof selector === "string" && rneedsContext.test( selector ) ?
				jQuery( selector ) :
				selector || [],
			false
		).length;
	}
} );


// Initialize a jQuery object


// A central reference to the root jQuery(document)
var rootjQuery,

	// A simple way to check for HTML strings
	// Prioritize #id over <tag> to avoid XSS via location.hash (#9521)
	// Strict HTML recognition (#11290: must start with <)
	// Shortcut simple #id case for speed
	rquickExpr = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]+))$/,

	init = jQuery.fn.init = function( selector, context, root ) {
		var match, elem;

		// HANDLE: $(""), $(null), $(undefined), $(false)
		if ( !selector ) {
			return this;
		}

		// Method init() accepts an alternate rootjQuery
		// so migrate can support jQuery.sub (gh-2101)
		root = root || rootjQuery;

		// Handle HTML strings
		if ( typeof selector === "string" ) {
			if ( selector[ 0 ] === "<" &&
				selector[ selector.length - 1 ] === ">" &&
				selector.length >= 3 ) {

				// Assume that strings that start and end with <> are HTML and skip the regex check
				match = [ null, selector, null ];

			} else {
				match = rquickExpr.exec( selector );
			}

			// Match html or make sure no context is specified for #id
			if ( match && ( match[ 1 ] || !context ) ) {

				// HANDLE: $(html) -> $(array)
				if ( match[ 1 ] ) {
					context = context instanceof jQuery ? context[ 0 ] : context;

					// Option to run scripts is true for back-compat
					// Intentionally let the error be thrown if parseHTML is not present
					jQuery.merge( this, jQuery.parseHTML(
						match[ 1 ],
						context && context.nodeType ? context.ownerDocument || context : document,
						true
					) );

					// HANDLE: $(html, props)
					if ( rsingleTag.test( match[ 1 ] ) && jQuery.isPlainObject( context ) ) {
						for ( match in context ) {

							// Properties of context are called as methods if possible
							if ( jQuery.isFunction( this[ match ] ) ) {
								this[ match ]( context[ match ] );

							// ...and otherwise set as attributes
							} else {
								this.attr( match, context[ match ] );
							}
						}
					}

					return this;

				// HANDLE: $(#id)
				} else {
					elem = document.getElementById( match[ 2 ] );

					if ( elem ) {

						// Inject the element directly into the jQuery object
						this[ 0 ] = elem;
						this.length = 1;
					}
					return this;
				}

			// HANDLE: $(expr, $(...))
			} else if ( !context || context.jquery ) {
				return ( context || root ).find( selector );

			// HANDLE: $(expr, context)
			// (which is just equivalent to: $(context).find(expr)
			} else {
				return this.constructor( context ).find( selector );
			}

		// HANDLE: $(DOMElement)
		} else if ( selector.nodeType ) {
			this[ 0 ] = selector;
			this.length = 1;
			return this;

		// HANDLE: $(function)
		// Shortcut for document ready
		} else if ( jQuery.isFunction( selector ) ) {
			return root.ready !== undefined ?
				root.ready( selector ) :

				// Execute immediately if ready is not present
				selector( jQuery );
		}

		return jQuery.makeArray( selector, this );
	};

// Give the init function the jQuery prototype for later instantiation
init.prototype = jQuery.fn;

// Initialize central reference
rootjQuery = jQuery( document );


var rparentsprev = /^(?:parents|prev(?:Until|All))/,

	// Methods guaranteed to produce a unique set when starting from a unique set
	guaranteedUnique = {
		children: true,
		contents: true,
		next: true,
		prev: true
	};

jQuery.fn.extend( {
	has: function( target ) {
		var targets = jQuery( target, this ),
			l = targets.length;

		return this.filter( function() {
			var i = 0;
			for ( ; i < l; i++ ) {
				if ( jQuery.contains( this, targets[ i ] ) ) {
					return true;
				}
			}
		} );
	},

	closest: function( selectors, context ) {
		var cur,
			i = 0,
			l = this.length,
			matched = [],
			targets = typeof selectors !== "string" && jQuery( selectors );

		// Positional selectors never match, since there's no _selection_ context
		if ( !rneedsContext.test( selectors ) ) {
			for ( ; i < l; i++ ) {
				for ( cur = this[ i ]; cur && cur !== context; cur = cur.parentNode ) {

					// Always skip document fragments
					if ( cur.nodeType < 11 && ( targets ?
						targets.index( cur ) > -1 :

						// Don't pass non-elements to Sizzle
						cur.nodeType === 1 &&
							jQuery.find.matchesSelector( cur, selectors ) ) ) {

						matched.push( cur );
						break;
					}
				}
			}
		}

		return this.pushStack( matched.length > 1 ? jQuery.uniqueSort( matched ) : matched );
	},

	// Determine the position of an element within the set
	index: function( elem ) {

		// No argument, return index in parent
		if ( !elem ) {
			return ( this[ 0 ] && this[ 0 ].parentNode ) ? this.first().prevAll().length : -1;
		}

		// Index in selector
		if ( typeof elem === "string" ) {
			return indexOf.call( jQuery( elem ), this[ 0 ] );
		}

		// Locate the position of the desired element
		return indexOf.call( this,

			// If it receives a jQuery object, the first element is used
			elem.jquery ? elem[ 0 ] : elem
		);
	},

	add: function( selector, context ) {
		return this.pushStack(
			jQuery.uniqueSort(
				jQuery.merge( this.get(), jQuery( selector, context ) )
			)
		);
	},

	addBack: function( selector ) {
		return this.add( selector == null ?
			this.prevObject : this.prevObject.filter( selector )
		);
	}
} );

function sibling( cur, dir ) {
	while ( ( cur = cur[ dir ] ) && cur.nodeType !== 1 ) {}
	return cur;
}

jQuery.each( {
	parent: function( elem ) {
		var parent = elem.parentNode;
		return parent && parent.nodeType !== 11 ? parent : null;
	},
	parents: function( elem ) {
		return dir( elem, "parentNode" );
	},
	parentsUntil: function( elem, i, until ) {
		return dir( elem, "parentNode", until );
	},
	next: function( elem ) {
		return sibling( elem, "nextSibling" );
	},
	prev: function( elem ) {
		return sibling( elem, "previousSibling" );
	},
	nextAll: function( elem ) {
		return dir( elem, "nextSibling" );
	},
	prevAll: function( elem ) {
		return dir( elem, "previousSibling" );
	},
	nextUntil: function( elem, i, until ) {
		return dir( elem, "nextSibling", until );
	},
	prevUntil: function( elem, i, until ) {
		return dir( elem, "previousSibling", until );
	},
	siblings: function( elem ) {
		return siblings( ( elem.parentNode || {} ).firstChild, elem );
	},
	children: function( elem ) {
		return siblings( elem.firstChild );
	},
	contents: function( elem ) {
        if ( nodeName( elem, "iframe" ) ) {
            return elem.contentDocument;
        }

        // Support: IE 9 - 11 only, iOS 7 only, Android Browser <=4.3 only
        // Treat the template element as a regular one in browsers that
        // don't support it.
        if ( nodeName( elem, "template" ) ) {
            elem = elem.content || elem;
        }

        return jQuery.merge( [], elem.childNodes );
	}
}, function( name, fn ) {
	jQuery.fn[ name ] = function( until, selector ) {
		var matched = jQuery.map( this, fn, until );

		if ( name.slice( -5 ) !== "Until" ) {
			selector = until;
		}

		if ( selector && typeof selector === "string" ) {
			matched = jQuery.filter( selector, matched );
		}

		if ( this.length > 1 ) {

			// Remove duplicates
			if ( !guaranteedUnique[ name ] ) {
				jQuery.uniqueSort( matched );
			}

			// Reverse order for parents* and prev-derivatives
			if ( rparentsprev.test( name ) ) {
				matched.reverse();
			}
		}

		return this.pushStack( matched );
	};
} );
var rnothtmlwhite = ( /[^\x20\t\r\n\f]+/g );



// Convert String-formatted options into Object-formatted ones
function createOptions( options ) {
	var object = {};
	jQuery.each( options.match( rnothtmlwhite ) || [], function( _, flag ) {
		object[ flag ] = true;
	} );
	return object;
}

/*
 * Create a callback list using the following parameters:
 *
 *	options: an optional list of space-separated options that will change how
 *			the callback list behaves or a more traditional option object
 *
 * By default a callback list will act like an event callback list and can be
 * "fired" multiple times.
 *
 * Possible options:
 *
 *	once:			will ensure the callback list can only be fired once (like a Deferred)
 *
 *	memory:			will keep track of previous values and will call any callback added
 *					after the list has been fired right away with the latest "memorized"
 *					values (like a Deferred)
 *
 *	unique:			will ensure a callback can only be added once (no duplicate in the list)
 *
 *	stopOnFalse:	interrupt callings when a callback returns false
 *
 */
jQuery.Callbacks = function( options ) {

	// Convert options from String-formatted to Object-formatted if needed
	// (we check in cache first)
	options = typeof options === "string" ?
		createOptions( options ) :
		jQuery.extend( {}, options );

	var // Flag to know if list is currently firing
		firing,

		// Last fire value for non-forgettable lists
		memory,

		// Flag to know if list was already fired
		fired,

		// Flag to prevent firing
		locked,

		// Actual callback list
		list = [],

		// Queue of execution data for repeatable lists
		queue = [],

		// Index of currently firing callback (modified by add/remove as needed)
		firingIndex = -1,

		// Fire callbacks
		fire = function() {

			// Enforce single-firing
			locked = locked || options.once;

			// Execute callbacks for all pending executions,
			// respecting firingIndex overrides and runtime changes
			fired = firing = true;
			for ( ; queue.length; firingIndex = -1 ) {
				memory = queue.shift();
				while ( ++firingIndex < list.length ) {

					// Run callback and check for early termination
					if ( list[ firingIndex ].apply( memory[ 0 ], memory[ 1 ] ) === false &&
						options.stopOnFalse ) {

						// Jump to end and forget the data so .add doesn't re-fire
						firingIndex = list.length;
						memory = false;
					}
				}
			}

			// Forget the data if we're done with it
			if ( !options.memory ) {
				memory = false;
			}

			firing = false;

			// Clean up if we're done firing for good
			if ( locked ) {

				// Keep an empty list if we have data for future add calls
				if ( memory ) {
					list = [];

				// Otherwise, this object is spent
				} else {
					list = "";
				}
			}
		},

		// Actual Callbacks object
		self = {

			// Add a callback or a collection of callbacks to the list
			add: function() {
				if ( list ) {

					// If we have memory from a past run, we should fire after adding
					if ( memory && !firing ) {
						firingIndex = list.length - 1;
						queue.push( memory );
					}

					( function add( args ) {
						jQuery.each( args, function( _, arg ) {
							if ( jQuery.isFunction( arg ) ) {
								if ( !options.unique || !self.has( arg ) ) {
									list.push( arg );
								}
							} else if ( arg && arg.length && jQuery.type( arg ) !== "string" ) {

								// Inspect recursively
								add( arg );
							}
						} );
					} )( arguments );

					if ( memory && !firing ) {
						fire();
					}
				}
				return this;
			},

			// Remove a callback from the list
			remove: function() {
				jQuery.each( arguments, function( _, arg ) {
					var index;
					while ( ( index = jQuery.inArray( arg, list, index ) ) > -1 ) {
						list.splice( index, 1 );

						// Handle firing indexes
						if ( index <= firingIndex ) {
							firingIndex--;
						}
					}
				} );
				return this;
			},

			// Check if a given callback is in the list.
			// If no argument is given, return whether or not list has callbacks attached.
			has: function( fn ) {
				return fn ?
					jQuery.inArray( fn, list ) > -1 :
					list.length > 0;
			},

			// Remove all callbacks from the list
			empty: function() {
				if ( list ) {
					list = [];
				}
				return this;
			},

			// Disable .fire and .add
			// Abort any current/pending executions
			// Clear all callbacks and values
			disable: function() {
				locked = queue = [];
				list = memory = "";
				return this;
			},
			disabled: function() {
				return !list;
			},

			// Disable .fire
			// Also disable .add unless we have memory (since it would have no effect)
			// Abort any pending executions
			lock: function() {
				locked = queue = [];
				if ( !memory && !firing ) {
					list = memory = "";
				}
				return this;
			},
			locked: function() {
				return !!locked;
			},

			// Call all callbacks with the given context and arguments
			fireWith: function( context, args ) {
				if ( !locked ) {
					args = args || [];
					args = [ context, args.slice ? args.slice() : args ];
					queue.push( args );
					if ( !firing ) {
						fire();
					}
				}
				return this;
			},

			// Call all the callbacks with the given arguments
			fire: function() {
				self.fireWith( this, arguments );
				return this;
			},

			// To know if the callbacks have already been called at least once
			fired: function() {
				return !!fired;
			}
		};

	return self;
};


function Identity( v ) {
	return v;
}
function Thrower( ex ) {
	throw ex;
}

function adoptValue( value, resolve, reject, noValue ) {
	var method;

	try {

		// Check for promise aspect first to privilege synchronous behavior
		if ( value && jQuery.isFunction( ( method = value.promise ) ) ) {
			method.call( value ).done( resolve ).fail( reject );

		// Other thenables
		} else if ( value && jQuery.isFunction( ( method = value.then ) ) ) {
			method.call( value, resolve, reject );

		// Other non-thenables
		} else {

			// Control `resolve` arguments by letting Array#slice cast boolean `noValue` to integer:
			// * false: [ value ].slice( 0 ) => resolve( value )
			// * true: [ value ].slice( 1 ) => resolve()
			resolve.apply( undefined, [ value ].slice( noValue ) );
		}

	// For Promises/A+, convert exceptions into rejections
	// Since jQuery.when doesn't unwrap thenables, we can skip the extra checks appearing in
	// Deferred#then to conditionally suppress rejection.
	} catch ( value ) {

		// Support: Android 4.0 only
		// Strict mode functions invoked without .call/.apply get global-object context
		reject.apply( undefined, [ value ] );
	}
}

jQuery.extend( {

	Deferred: function( func ) {
		var tuples = [

				// action, add listener, callbacks,
				// ... .then handlers, argument index, [final state]
				[ "notify", "progress", jQuery.Callbacks( "memory" ),
					jQuery.Callbacks( "memory" ), 2 ],
				[ "resolve", "done", jQuery.Callbacks( "once memory" ),
					jQuery.Callbacks( "once memory" ), 0, "resolved" ],
				[ "reject", "fail", jQuery.Callbacks( "once memory" ),
					jQuery.Callbacks( "once memory" ), 1, "rejected" ]
			],
			state = "pending",
			promise = {
				state: function() {
					return state;
				},
				always: function() {
					deferred.done( arguments ).fail( arguments );
					return this;
				},
				"catch": function( fn ) {
					return promise.then( null, fn );
				},

				// Keep pipe for back-compat
				pipe: function( /* fnDone, fnFail, fnProgress */ ) {
					var fns = arguments;

					return jQuery.Deferred( function( newDefer ) {
						jQuery.each( tuples, function( i, tuple ) {

							// Map tuples (progress, done, fail) to arguments (done, fail, progress)
							var fn = jQuery.isFunction( fns[ tuple[ 4 ] ] ) && fns[ tuple[ 4 ] ];

							// deferred.progress(function() { bind to newDefer or newDefer.notify })
							// deferred.done(function() { bind to newDefer or newDefer.resolve })
							// deferred.fail(function() { bind to newDefer or newDefer.reject })
							deferred[ tuple[ 1 ] ]( function() {
								var returned = fn && fn.apply( this, arguments );
								if ( returned && jQuery.isFunction( returned.promise ) ) {
									returned.promise()
										.progress( newDefer.notify )
										.done( newDefer.resolve )
										.fail( newDefer.reject );
								} else {
									newDefer[ tuple[ 0 ] + "With" ](
										this,
										fn ? [ returned ] : arguments
									);
								}
							} );
						} );
						fns = null;
					} ).promise();
				},
				then: function( onFulfilled, onRejected, onProgress ) {
					var maxDepth = 0;
					function resolve( depth, deferred, handler, special ) {
						return function() {
							var that = this,
								args = arguments,
								mightThrow = function() {
									var returned, then;

									// Support: Promises/A+ section 2.3.3.3.3
									// https://promisesaplus.com/#point-59
									// Ignore double-resolution attempts
									if ( depth < maxDepth ) {
										return;
									}

									returned = handler.apply( that, args );

									// Support: Promises/A+ section 2.3.1
									// https://promisesaplus.com/#point-48
									if ( returned === deferred.promise() ) {
										throw new TypeError( "Thenable self-resolution" );
									}

									// Support: Promises/A+ sections 2.3.3.1, 3.5
									// https://promisesaplus.com/#point-54
									// https://promisesaplus.com/#point-75
									// Retrieve `then` only once
									then = returned &&

										// Support: Promises/A+ section 2.3.4
										// https://promisesaplus.com/#point-64
										// Only check objects and functions for thenability
										( typeof returned === "object" ||
											typeof returned === "function" ) &&
										returned.then;

									// Handle a returned thenable
									if ( jQuery.isFunction( then ) ) {

										// Special processors (notify) just wait for resolution
										if ( special ) {
											then.call(
												returned,
												resolve( maxDepth, deferred, Identity, special ),
												resolve( maxDepth, deferred, Thrower, special )
											);

										// Normal processors (resolve) also hook into progress
										} else {

											// ...and disregard older resolution values
											maxDepth++;

											then.call(
												returned,
												resolve( maxDepth, deferred, Identity, special ),
												resolve( maxDepth, deferred, Thrower, special ),
												resolve( maxDepth, deferred, Identity,
													deferred.notifyWith )
											);
										}

									// Handle all other returned values
									} else {

										// Only substitute handlers pass on context
										// and multiple values (non-spec behavior)
										if ( handler !== Identity ) {
											that = undefined;
											args = [ returned ];
										}

										// Process the value(s)
										// Default process is resolve
										( special || deferred.resolveWith )( that, args );
									}
								},

								// Only normal processors (resolve) catch and reject exceptions
								process = special ?
									mightThrow :
									function() {
										try {
											mightThrow();
										} catch ( e ) {

											if ( jQuery.Deferred.exceptionHook ) {
												jQuery.Deferred.exceptionHook( e,
													process.stackTrace );
											}

											// Support: Promises/A+ section 2.3.3.3.4.1
											// https://promisesaplus.com/#point-61
											// Ignore post-resolution exceptions
											if ( depth + 1 >= maxDepth ) {

												// Only substitute handlers pass on context
												// and multiple values (non-spec behavior)
												if ( handler !== Thrower ) {
													that = undefined;
													args = [ e ];
												}

												deferred.rejectWith( that, args );
											}
										}
									};

							// Support: Promises/A+ section 2.3.3.3.1
							// https://promisesaplus.com/#point-57
							// Re-resolve promises immediately to dodge false rejection from
							// subsequent errors
							if ( depth ) {
								process();
							} else {

								// Call an optional hook to record the stack, in case of exception
								// since it's otherwise lost when execution goes async
								if ( jQuery.Deferred.getStackHook ) {
									process.stackTrace = jQuery.Deferred.getStackHook();
								}
								window.setTimeout( process );
							}
						};
					}

					return jQuery.Deferred( function( newDefer ) {

						// progress_handlers.add( ... )
						tuples[ 0 ][ 3 ].add(
							resolve(
								0,
								newDefer,
								jQuery.isFunction( onProgress ) ?
									onProgress :
									Identity,
								newDefer.notifyWith
							)
						);

						// fulfilled_handlers.add( ... )
						tuples[ 1 ][ 3 ].add(
							resolve(
								0,
								newDefer,
								jQuery.isFunction( onFulfilled ) ?
									onFulfilled :
									Identity
							)
						);

						// rejected_handlers.add( ... )
						tuples[ 2 ][ 3 ].add(
							resolve(
								0,
								newDefer,
								jQuery.isFunction( onRejected ) ?
									onRejected :
									Thrower
							)
						);
					} ).promise();
				},

				// Get a promise for this deferred
				// If obj is provided, the promise aspect is added to the object
				promise: function( obj ) {
					return obj != null ? jQuery.extend( obj, promise ) : promise;
				}
			},
			deferred = {};

		// Add list-specific methods
		jQuery.each( tuples, function( i, tuple ) {
			var list = tuple[ 2 ],
				stateString = tuple[ 5 ];

			// promise.progress = list.add
			// promise.done = list.add
			// promise.fail = list.add
			promise[ tuple[ 1 ] ] = list.add;

			// Handle state
			if ( stateString ) {
				list.add(
					function() {

						// state = "resolved" (i.e., fulfilled)
						// state = "rejected"
						state = stateString;
					},

					// rejected_callbacks.disable
					// fulfilled_callbacks.disable
					tuples[ 3 - i ][ 2 ].disable,

					// progress_callbacks.lock
					tuples[ 0 ][ 2 ].lock
				);
			}

			// progress_handlers.fire
			// fulfilled_handlers.fire
			// rejected_handlers.fire
			list.add( tuple[ 3 ].fire );

			// deferred.notify = function() { deferred.notifyWith(...) }
			// deferred.resolve = function() { deferred.resolveWith(...) }
			// deferred.reject = function() { deferred.rejectWith(...) }
			deferred[ tuple[ 0 ] ] = function() {
				deferred[ tuple[ 0 ] + "With" ]( this === deferred ? undefined : this, arguments );
				return this;
			};

			// deferred.notifyWith = list.fireWith
			// deferred.resolveWith = list.fireWith
			// deferred.rejectWith = list.fireWith
			deferred[ tuple[ 0 ] + "With" ] = list.fireWith;
		} );

		// Make the deferred a promise
		promise.promise( deferred );

		// Call given func if any
		if ( func ) {
			func.call( deferred, deferred );
		}

		// All done!
		return deferred;
	},

	// Deferred helper
	when: function( singleValue ) {
		var

			// count of uncompleted subordinates
			remaining = arguments.length,

			// count of unprocessed arguments
			i = remaining,

			// subordinate fulfillment data
			resolveContexts = Array( i ),
			resolveValues = slice.call( arguments ),

			// the master Deferred
			master = jQuery.Deferred(),

			// subordinate callback factory
			updateFunc = function( i ) {
				return function( value ) {
					resolveContexts[ i ] = this;
					resolveValues[ i ] = arguments.length > 1 ? slice.call( arguments ) : value;
					if ( !( --remaining ) ) {
						master.resolveWith( resolveContexts, resolveValues );
					}
				};
			};

		// Single- and empty arguments are adopted like Promise.resolve
		if ( remaining <= 1 ) {
			adoptValue( singleValue, master.done( updateFunc( i ) ).resolve, master.reject,
				!remaining );

			// Use .then() to unwrap secondary thenables (cf. gh-3000)
			if ( master.state() === "pending" ||
				jQuery.isFunction( resolveValues[ i ] && resolveValues[ i ].then ) ) {

				return master.then();
			}
		}

		// Multiple arguments are aggregated like Promise.all array elements
		while ( i-- ) {
			adoptValue( resolveValues[ i ], updateFunc( i ), master.reject );
		}

		return master.promise();
	}
} );


// These usually indicate a programmer mistake during development,
// warn about them ASAP rather than swallowing them by default.
var rerrorNames = /^(Eval|Internal|Range|Reference|Syntax|Type|URI)Error$/;

jQuery.Deferred.exceptionHook = function( error, stack ) {

	// Support: IE 8 - 9 only
	// Console exists when dev tools are open, which can happen at any time
	if ( window.console && window.console.warn && error && rerrorNames.test( error.name ) ) {
		window.console.warn( "jQuery.Deferred exception: " + error.message, error.stack, stack );
	}
};




jQuery.readyException = function( error ) {
	window.setTimeout( function() {
		throw error;
	} );
};




// The deferred used on DOM ready
var readyList = jQuery.Deferred();

jQuery.fn.ready = function( fn ) {

	readyList
		.then( fn )

		// Wrap jQuery.readyException in a function so that the lookup
		// happens at the time of error handling instead of callback
		// registration.
		.catch( function( error ) {
			jQuery.readyException( error );
		} );

	return this;
};

jQuery.extend( {

	// Is the DOM ready to be used? Set to true once it occurs.
	isReady: false,

	// A counter to track how many items to wait for before
	// the ready event fires. See #6781
	readyWait: 1,

	// Handle when the DOM is ready
	ready: function( wait ) {

		// Abort if there are pending holds or we're already ready
		if ( wait === true ? --jQuery.readyWait : jQuery.isReady ) {
			return;
		}

		// Remember that the DOM is ready
		jQuery.isReady = true;

		// If a normal DOM Ready event fired, decrement, and wait if need be
		if ( wait !== true && --jQuery.readyWait > 0 ) {
			return;
		}

		// If there are functions bound, to execute
		readyList.resolveWith( document, [ jQuery ] );
	}
} );

jQuery.ready.then = readyList.then;

// The ready event handler and self cleanup method
function completed() {
	document.removeEventListener( "DOMContentLoaded", completed );
	window.removeEventListener( "load", completed );
	jQuery.ready();
}

// Catch cases where $(document).ready() is called
// after the browser event has already occurred.
// Support: IE <=9 - 10 only
// Older IE sometimes signals "interactive" too soon
if ( document.readyState === "complete" ||
	( document.readyState !== "loading" && !document.documentElement.doScroll ) ) {

	// Handle it asynchronously to allow scripts the opportunity to delay ready
	window.setTimeout( jQuery.ready );

} else {

	// Use the handy event callback
	document.addEventListener( "DOMContentLoaded", completed );

	// A fallback to window.onload, that will always work
	window.addEventListener( "load", completed );
}




// Multifunctional method to get and set values of a collection
// The value/s can optionally be executed if it's a function
var access = function( elems, fn, key, value, chainable, emptyGet, raw ) {
	var i = 0,
		len = elems.length,
		bulk = key == null;

	// Sets many values
	if ( jQuery.type( key ) === "object" ) {
		chainable = true;
		for ( i in key ) {
			access( elems, fn, i, key[ i ], true, emptyGet, raw );
		}

	// Sets one value
	} else if ( value !== undefined ) {
		chainable = true;

		if ( !jQuery.isFunction( value ) ) {
			raw = true;
		}

		if ( bulk ) {

			// Bulk operations run against the entire set
			if ( raw ) {
				fn.call( elems, value );
				fn = null;

			// ...except when executing function values
			} else {
				bulk = fn;
				fn = function( elem, key, value ) {
					return bulk.call( jQuery( elem ), value );
				};
			}
		}

		if ( fn ) {
			for ( ; i < len; i++ ) {
				fn(
					elems[ i ], key, raw ?
					value :
					value.call( elems[ i ], i, fn( elems[ i ], key ) )
				);
			}
		}
	}

	if ( chainable ) {
		return elems;
	}

	// Gets
	if ( bulk ) {
		return fn.call( elems );
	}

	return len ? fn( elems[ 0 ], key ) : emptyGet;
};
var acceptData = function( owner ) {

	// Accepts only:
	//  - Node
	//    - Node.ELEMENT_NODE
	//    - Node.DOCUMENT_NODE
	//  - Object
	//    - Any
	return owner.nodeType === 1 || owner.nodeType === 9 || !( +owner.nodeType );
};




function Data() {
	this.expando = jQuery.expando + Data.uid++;
}

Data.uid = 1;

Data.prototype = {

	cache: function( owner ) {

		// Check if the owner object already has a cache
		var value = owner[ this.expando ];

		// If not, create one
		if ( !value ) {
			value = {};

			// We can accept data for non-element nodes in modern browsers,
			// but we should not, see #8335.
			// Always return an empty object.
			if ( acceptData( owner ) ) {

				// If it is a node unlikely to be stringify-ed or looped over
				// use plain assignment
				if ( owner.nodeType ) {
					owner[ this.expando ] = value;

				// Otherwise secure it in a non-enumerable property
				// configurable must be true to allow the property to be
				// deleted when data is removed
				} else {
					Object.defineProperty( owner, this.expando, {
						value: value,
						configurable: true
					} );
				}
			}
		}

		return value;
	},
	set: function( owner, data, value ) {
		var prop,
			cache = this.cache( owner );

		// Handle: [ owner, key, value ] args
		// Always use camelCase key (gh-2257)
		if ( typeof data === "string" ) {
			cache[ jQuery.camelCase( data ) ] = value;

		// Handle: [ owner, { properties } ] args
		} else {

			// Copy the properties one-by-one to the cache object
			for ( prop in data ) {
				cache[ jQuery.camelCase( prop ) ] = data[ prop ];
			}
		}
		return cache;
	},
	get: function( owner, key ) {
		return key === undefined ?
			this.cache( owner ) :

			// Always use camelCase key (gh-2257)
			owner[ this.expando ] && owner[ this.expando ][ jQuery.camelCase( key ) ];
	},
	access: function( owner, key, value ) {

		// In cases where either:
		//
		//   1. No key was specified
		//   2. A string key was specified, but no value provided
		//
		// Take the "read" path and allow the get method to determine
		// which value to return, respectively either:
		//
		//   1. The entire cache object
		//   2. The data stored at the key
		//
		if ( key === undefined ||
				( ( key && typeof key === "string" ) && value === undefined ) ) {

			return this.get( owner, key );
		}

		// When the key is not a string, or both a key and value
		// are specified, set or extend (existing objects) with either:
		//
		//   1. An object of properties
		//   2. A key and value
		//
		this.set( owner, key, value );

		// Since the "set" path can have two possible entry points
		// return the expected data based on which path was taken[*]
		return value !== undefined ? value : key;
	},
	remove: function( owner, key ) {
		var i,
			cache = owner[ this.expando ];

		if ( cache === undefined ) {
			return;
		}

		if ( key !== undefined ) {

			// Support array or space separated string of keys
			if ( Array.isArray( key ) ) {

				// If key is an array of keys...
				// We always set camelCase keys, so remove that.
				key = key.map( jQuery.camelCase );
			} else {
				key = jQuery.camelCase( key );

				// If a key with the spaces exists, use it.
				// Otherwise, create an array by matching non-whitespace
				key = key in cache ?
					[ key ] :
					( key.match( rnothtmlwhite ) || [] );
			}

			i = key.length;

			while ( i-- ) {
				delete cache[ key[ i ] ];
			}
		}

		// Remove the expando if there's no more data
		if ( key === undefined || jQuery.isEmptyObject( cache ) ) {

			// Support: Chrome <=35 - 45
			// Webkit & Blink performance suffers when deleting properties
			// from DOM nodes, so set to undefined instead
			// https://bugs.chromium.org/p/chromium/issues/detail?id=378607 (bug restricted)
			if ( owner.nodeType ) {
				owner[ this.expando ] = undefined;
			} else {
				delete owner[ this.expando ];
			}
		}
	},
	hasData: function( owner ) {
		var cache = owner[ this.expando ];
		return cache !== undefined && !jQuery.isEmptyObject( cache );
	}
};
var dataPriv = new Data();

var dataUser = new Data();



//	Implementation Summary
//
//	1. Enforce API surface and semantic compatibility with 1.9.x branch
//	2. Improve the module's maintainability by reducing the storage
//		paths to a single mechanism.
//	3. Use the same single mechanism to support "private" and "user" data.
//	4. _Never_ expose "private" data to user code (TODO: Drop _data, _removeData)
//	5. Avoid exposing implementation details on user objects (eg. expando properties)
//	6. Provide a clear path for implementation upgrade to WeakMap in 2014

var rbrace = /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,
	rmultiDash = /[A-Z]/g;

function getData( data ) {
	if ( data === "true" ) {
		return true;
	}

	if ( data === "false" ) {
		return false;
	}

	if ( data === "null" ) {
		return null;
	}

	// Only convert to a number if it doesn't change the string
	if ( data === +data + "" ) {
		return +data;
	}

	if ( rbrace.test( data ) ) {
		return JSON.parse( data );
	}

	return data;
}

function dataAttr( elem, key, data ) {
	var name;

	// If nothing was found internally, try to fetch any
	// data from the HTML5 data-* attribute
	if ( data === undefined && elem.nodeType === 1 ) {
		name = "data-" + key.replace( rmultiDash, "-$&" ).toLowerCase();
		data = elem.getAttribute( name );

		if ( typeof data === "string" ) {
			try {
				data = getData( data );
			} catch ( e ) {}

			// Make sure we set the data so it isn't changed later
			dataUser.set( elem, key, data );
		} else {
			data = undefined;
		}
	}
	return data;
}

jQuery.extend( {
	hasData: function( elem ) {
		return dataUser.hasData( elem ) || dataPriv.hasData( elem );
	},

	data: function( elem, name, data ) {
		return dataUser.access( elem, name, data );
	},

	removeData: function( elem, name ) {
		dataUser.remove( elem, name );
	},

	// TODO: Now that all calls to _data and _removeData have been replaced
	// with direct calls to dataPriv methods, these can be deprecated.
	_data: function( elem, name, data ) {
		return dataPriv.access( elem, name, data );
	},

	_removeData: function( elem, name ) {
		dataPriv.remove( elem, name );
	}
} );

jQuery.fn.extend( {
	data: function( key, value ) {
		var i, name, data,
			elem = this[ 0 ],
			attrs = elem && elem.attributes;

		// Gets all values
		if ( key === undefined ) {
			if ( this.length ) {
				data = dataUser.get( elem );

				if ( elem.nodeType === 1 && !dataPriv.get( elem, "hasDataAttrs" ) ) {
					i = attrs.length;
					while ( i-- ) {

						// Support: IE 11 only
						// The attrs elements can be null (#14894)
						if ( attrs[ i ] ) {
							name = attrs[ i ].name;
							if ( name.indexOf( "data-" ) === 0 ) {
								name = jQuery.camelCase( name.slice( 5 ) );
								dataAttr( elem, name, data[ name ] );
							}
						}
					}
					dataPriv.set( elem, "hasDataAttrs", true );
				}
			}

			return data;
		}

		// Sets multiple values
		if ( typeof key === "object" ) {
			return this.each( function() {
				dataUser.set( this, key );
			} );
		}

		return access( this, function( value ) {
			var data;

			// The calling jQuery object (element matches) is not empty
			// (and therefore has an element appears at this[ 0 ]) and the
			// `value` parameter was not undefined. An empty jQuery object
			// will result in `undefined` for elem = this[ 0 ] which will
			// throw an exception if an attempt to read a data cache is made.
			if ( elem && value === undefined ) {

				// Attempt to get data from the cache
				// The key will always be camelCased in Data
				data = dataUser.get( elem, key );
				if ( data !== undefined ) {
					return data;
				}

				// Attempt to "discover" the data in
				// HTML5 custom data-* attrs
				data = dataAttr( elem, key );
				if ( data !== undefined ) {
					return data;
				}

				// We tried really hard, but the data doesn't exist.
				return;
			}

			// Set the data...
			this.each( function() {

				// We always store the camelCased key
				dataUser.set( this, key, value );
			} );
		}, null, value, arguments.length > 1, null, true );
	},

	removeData: function( key ) {
		return this.each( function() {
			dataUser.remove( this, key );
		} );
	}
} );


jQuery.extend( {
	queue: function( elem, type, data ) {
		var queue;

		if ( elem ) {
			type = ( type || "fx" ) + "queue";
			queue = dataPriv.get( elem, type );

			// Speed up dequeue by getting out quickly if this is just a lookup
			if ( data ) {
				if ( !queue || Array.isArray( data ) ) {
					queue = dataPriv.access( elem, type, jQuery.makeArray( data ) );
				} else {
					queue.push( data );
				}
			}
			return queue || [];
		}
	},

	dequeue: function( elem, type ) {
		type = type || "fx";

		var queue = jQuery.queue( elem, type ),
			startLength = queue.length,
			fn = queue.shift(),
			hooks = jQuery._queueHooks( elem, type ),
			next = function() {
				jQuery.dequeue( elem, type );
			};

		// If the fx queue is dequeued, always remove the progress sentinel
		if ( fn === "inprogress" ) {
			fn = queue.shift();
			startLength--;
		}

		if ( fn ) {

			// Add a progress sentinel to prevent the fx queue from being
			// automatically dequeued
			if ( type === "fx" ) {
				queue.unshift( "inprogress" );
			}

			// Clear up the last queue stop function
			delete hooks.stop;
			fn.call( elem, next, hooks );
		}

		if ( !startLength && hooks ) {
			hooks.empty.fire();
		}
	},

	// Not public - generate a queueHooks object, or return the current one
	_queueHooks: function( elem, type ) {
		var key = type + "queueHooks";
		return dataPriv.get( elem, key ) || dataPriv.access( elem, key, {
			empty: jQuery.Callbacks( "once memory" ).add( function() {
				dataPriv.remove( elem, [ type + "queue", key ] );
			} )
		} );
	}
} );

jQuery.fn.extend( {
	queue: function( type, data ) {
		var setter = 2;

		if ( typeof type !== "string" ) {
			data = type;
			type = "fx";
			setter--;
		}

		if ( arguments.length < setter ) {
			return jQuery.queue( this[ 0 ], type );
		}

		return data === undefined ?
			this :
			this.each( function() {
				var queue = jQuery.queue( this, type, data );

				// Ensure a hooks for this queue
				jQuery._queueHooks( this, type );

				if ( type === "fx" && queue[ 0 ] !== "inprogress" ) {
					jQuery.dequeue( this, type );
				}
			} );
	},
	dequeue: function( type ) {
		return this.each( function() {
			jQuery.dequeue( this, type );
		} );
	},
	clearQueue: function( type ) {
		return this.queue( type || "fx", [] );
	},

	// Get a promise resolved when queues of a certain type
	// are emptied (fx is the type by default)
	promise: function( type, obj ) {
		var tmp,
			count = 1,
			defer = jQuery.Deferred(),
			elements = this,
			i = this.length,
			resolve = function() {
				if ( !( --count ) ) {
					defer.resolveWith( elements, [ elements ] );
				}
			};

		if ( typeof type !== "string" ) {
			obj = type;
			type = undefined;
		}
		type = type || "fx";

		while ( i-- ) {
			tmp = dataPriv.get( elements[ i ], type + "queueHooks" );
			if ( tmp && tmp.empty ) {
				count++;
				tmp.empty.add( resolve );
			}
		}
		resolve();
		return defer.promise( obj );
	}
} );
var pnum = ( /[+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|)/ ).source;

var rcssNum = new RegExp( "^(?:([+-])=|)(" + pnum + ")([a-z%]*)$", "i" );


var cssExpand = [ "Top", "Right", "Bottom", "Left" ];

var isHiddenWithinTree = function( elem, el ) {

		// isHiddenWithinTree might be called from jQuery#filter function;
		// in that case, element will be second argument
		elem = el || elem;

		// Inline style trumps all
		return elem.style.display === "none" ||
			elem.style.display === "" &&

			// Otherwise, check computed style
			// Support: Firefox <=43 - 45
			// Disconnected elements can have computed display: none, so first confirm that elem is
			// in the document.
			jQuery.contains( elem.ownerDocument, elem ) &&

			jQuery.css( elem, "display" ) === "none";
	};

var swap = function( elem, options, callback, args ) {
	var ret, name,
		old = {};

	// Remember the old values, and insert the new ones
	for ( name in options ) {
		old[ name ] = elem.style[ name ];
		elem.style[ name ] = options[ name ];
	}

	ret = callback.apply( elem, args || [] );

	// Revert the old values
	for ( name in options ) {
		elem.style[ name ] = old[ name ];
	}

	return ret;
};




function adjustCSS( elem, prop, valueParts, tween ) {
	var adjusted,
		scale = 1,
		maxIterations = 20,
		currentValue = tween ?
			function() {
				return tween.cur();
			} :
			function() {
				return jQuery.css( elem, prop, "" );
			},
		initial = currentValue(),
		unit = valueParts && valueParts[ 3 ] || ( jQuery.cssNumber[ prop ] ? "" : "px" ),

		// Starting value computation is required for potential unit mismatches
		initialInUnit = ( jQuery.cssNumber[ prop ] || unit !== "px" && +initial ) &&
			rcssNum.exec( jQuery.css( elem, prop ) );

	if ( initialInUnit && initialInUnit[ 3 ] !== unit ) {

		// Trust units reported by jQuery.css
		unit = unit || initialInUnit[ 3 ];

		// Make sure we update the tween properties later on
		valueParts = valueParts || [];

		// Iteratively approximate from a nonzero starting point
		initialInUnit = +initial || 1;

		do {

			// If previous iteration zeroed out, double until we get *something*.
			// Use string for doubling so we don't accidentally see scale as unchanged below
			scale = scale || ".5";

			// Adjust and apply
			initialInUnit = initialInUnit / scale;
			jQuery.style( elem, prop, initialInUnit + unit );

		// Update scale, tolerating zero or NaN from tween.cur()
		// Break the loop if scale is unchanged or perfect, or if we've just had enough.
		} while (
			scale !== ( scale = currentValue() / initial ) && scale !== 1 && --maxIterations
		);
	}

	if ( valueParts ) {
		initialInUnit = +initialInUnit || +initial || 0;

		// Apply relative offset (+=/-=) if specified
		adjusted = valueParts[ 1 ] ?
			initialInUnit + ( valueParts[ 1 ] + 1 ) * valueParts[ 2 ] :
			+valueParts[ 2 ];
		if ( tween ) {
			tween.unit = unit;
			tween.start = initialInUnit;
			tween.end = adjusted;
		}
	}
	return adjusted;
}


var defaultDisplayMap = {};

function getDefaultDisplay( elem ) {
	var temp,
		doc = elem.ownerDocument,
		nodeName = elem.nodeName,
		display = defaultDisplayMap[ nodeName ];

	if ( display ) {
		return display;
	}

	temp = doc.body.appendChild( doc.createElement( nodeName ) );
	display = jQuery.css( temp, "display" );

	temp.parentNode.removeChild( temp );

	if ( display === "none" ) {
		display = "block";
	}
	defaultDisplayMap[ nodeName ] = display;

	return display;
}

function showHide( elements, show ) {
	var display, elem,
		values = [],
		index = 0,
		length = elements.length;

	// Determine new display value for elements that need to change
	for ( ; index < length; index++ ) {
		elem = elements[ index ];
		if ( !elem.style ) {
			continue;
		}

		display = elem.style.display;
		if ( show ) {

			// Since we force visibility upon cascade-hidden elements, an immediate (and slow)
			// check is required in this first loop unless we have a nonempty display value (either
			// inline or about-to-be-restored)
			if ( display === "none" ) {
				values[ index ] = dataPriv.get( elem, "display" ) || null;
				if ( !values[ index ] ) {
					elem.style.display = "";
				}
			}
			if ( elem.style.display === "" && isHiddenWithinTree( elem ) ) {
				values[ index ] = getDefaultDisplay( elem );
			}
		} else {
			if ( display !== "none" ) {
				values[ index ] = "none";

				// Remember what we're overwriting
				dataPriv.set( elem, "display", display );
			}
		}
	}

	// Set the display of the elements in a second loop to avoid constant reflow
	for ( index = 0; index < length; index++ ) {
		if ( values[ index ] != null ) {
			elements[ index ].style.display = values[ index ];
		}
	}

	return elements;
}

jQuery.fn.extend( {
	show: function() {
		return showHide( this, true );
	},
	hide: function() {
		return showHide( this );
	},
	toggle: function( state ) {
		if ( typeof state === "boolean" ) {
			return state ? this.show() : this.hide();
		}

		return this.each( function() {
			if ( isHiddenWithinTree( this ) ) {
				jQuery( this ).show();
			} else {
				jQuery( this ).hide();
			}
		} );
	}
} );
var rcheckableType = ( /^(?:checkbox|radio)$/i );

var rtagName = ( /<([a-z][^\/\0>\x20\t\r\n\f]+)/i );

var rscriptType = ( /^$|\/(?:java|ecma)script/i );



// We have to close these tags to support XHTML (#13200)
var wrapMap = {

	// Support: IE <=9 only
	option: [ 1, "<select multiple='multiple'>", "</select>" ],

	// XHTML parsers do not magically insert elements in the
	// same way that tag soup parsers do. So we cannot shorten
	// this by omitting <tbody> or other required elements.
	thead: [ 1, "<table>", "</table>" ],
	col: [ 2, "<table><colgroup>", "</colgroup></table>" ],
	tr: [ 2, "<table><tbody>", "</tbody></table>" ],
	td: [ 3, "<table><tbody><tr>", "</tr></tbody></table>" ],

	_default: [ 0, "", "" ]
};

// Support: IE <=9 only
wrapMap.optgroup = wrapMap.option;

wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
wrapMap.th = wrapMap.td;


function getAll( context, tag ) {

	// Support: IE <=9 - 11 only
	// Use typeof to avoid zero-argument method invocation on host objects (#15151)
	var ret;

	if ( typeof context.getElementsByTagName !== "undefined" ) {
		ret = context.getElementsByTagName( tag || "*" );

	} else if ( typeof context.querySelectorAll !== "undefined" ) {
		ret = context.querySelectorAll( tag || "*" );

	} else {
		ret = [];
	}

	if ( tag === undefined || tag && nodeName( context, tag ) ) {
		return jQuery.merge( [ context ], ret );
	}

	return ret;
}


// Mark scripts as having already been evaluated
function setGlobalEval( elems, refElements ) {
	var i = 0,
		l = elems.length;

	for ( ; i < l; i++ ) {
		dataPriv.set(
			elems[ i ],
			"globalEval",
			!refElements || dataPriv.get( refElements[ i ], "globalEval" )
		);
	}
}


var rhtml = /<|&#?\w+;/;

function buildFragment( elems, context, scripts, selection, ignored ) {
	var elem, tmp, tag, wrap, contains, j,
		fragment = context.createDocumentFragment(),
		nodes = [],
		i = 0,
		l = elems.length;

	for ( ; i < l; i++ ) {
		elem = elems[ i ];

		if ( elem || elem === 0 ) {

			// Add nodes directly
			if ( jQuery.type( elem ) === "object" ) {

				// Support: Android <=4.0 only, PhantomJS 1 only
				// push.apply(_, arraylike) throws on ancient WebKit
				jQuery.merge( nodes, elem.nodeType ? [ elem ] : elem );

			// Convert non-html into a text node
			} else if ( !rhtml.test( elem ) ) {
				nodes.push( context.createTextNode( elem ) );

			// Convert html into DOM nodes
			} else {
				tmp = tmp || fragment.appendChild( context.createElement( "div" ) );

				// Deserialize a standard representation
				tag = ( rtagName.exec( elem ) || [ "", "" ] )[ 1 ].toLowerCase();
				wrap = wrapMap[ tag ] || wrapMap._default;
				tmp.innerHTML = wrap[ 1 ] + jQuery.htmlPrefilter( elem ) + wrap[ 2 ];

				// Descend through wrappers to the right content
				j = wrap[ 0 ];
				while ( j-- ) {
					tmp = tmp.lastChild;
				}

				// Support: Android <=4.0 only, PhantomJS 1 only
				// push.apply(_, arraylike) throws on ancient WebKit
				jQuery.merge( nodes, tmp.childNodes );

				// Remember the top-level container
				tmp = fragment.firstChild;

				// Ensure the created nodes are orphaned (#12392)
				tmp.textContent = "";
			}
		}
	}

	// Remove wrapper from fragment
	fragment.textContent = "";

	i = 0;
	while ( ( elem = nodes[ i++ ] ) ) {

		// Skip elements already in the context collection (trac-4087)
		if ( selection && jQuery.inArray( elem, selection ) > -1 ) {
			if ( ignored ) {
				ignored.push( elem );
			}
			continue;
		}

		contains = jQuery.contains( elem.ownerDocument, elem );

		// Append to fragment
		tmp = getAll( fragment.appendChild( elem ), "script" );

		// Preserve script evaluation history
		if ( contains ) {
			setGlobalEval( tmp );
		}

		// Capture executables
		if ( scripts ) {
			j = 0;
			while ( ( elem = tmp[ j++ ] ) ) {
				if ( rscriptType.test( elem.type || "" ) ) {
					scripts.push( elem );
				}
			}
		}
	}

	return fragment;
}


( function() {
	var fragment = document.createDocumentFragment(),
		div = fragment.appendChild( document.createElement( "div" ) ),
		input = document.createElement( "input" );

	// Support: Android 4.0 - 4.3 only
	// Check state lost if the name is set (#11217)
	// Support: Windows Web Apps (WWA)
	// `name` and `type` must use .setAttribute for WWA (#14901)
	input.setAttribute( "type", "radio" );
	input.setAttribute( "checked", "checked" );
	input.setAttribute( "name", "t" );

	div.appendChild( input );

	// Support: Android <=4.1 only
	// Older WebKit doesn't clone checked state correctly in fragments
	support.checkClone = div.cloneNode( true ).cloneNode( true ).lastChild.checked;

	// Support: IE <=11 only
	// Make sure textarea (and checkbox) defaultValue is properly cloned
	div.innerHTML = "<textarea>x</textarea>";
	support.noCloneChecked = !!div.cloneNode( true ).lastChild.defaultValue;
} )();
var documentElement = document.documentElement;



var
	rkeyEvent = /^key/,
	rmouseEvent = /^(?:mouse|pointer|contextmenu|drag|drop)|click/,
	rtypenamespace = /^([^.]*)(?:\.(.+)|)/;

function returnTrue() {
	return true;
}

function returnFalse() {
	return false;
}

// Support: IE <=9 only
// See #13393 for more info
function safeActiveElement() {
	try {
		return document.activeElement;
	} catch ( err ) { }
}

function on( elem, types, selector, data, fn, one ) {
	var origFn, type;

	// Types can be a map of types/handlers
	if ( typeof types === "object" ) {

		// ( types-Object, selector, data )
		if ( typeof selector !== "string" ) {

			// ( types-Object, data )
			data = data || selector;
			selector = undefined;
		}
		for ( type in types ) {
			on( elem, type, selector, data, types[ type ], one );
		}
		return elem;
	}

	if ( data == null && fn == null ) {

		// ( types, fn )
		fn = selector;
		data = selector = undefined;
	} else if ( fn == null ) {
		if ( typeof selector === "string" ) {

			// ( types, selector, fn )
			fn = data;
			data = undefined;
		} else {

			// ( types, data, fn )
			fn = data;
			data = selector;
			selector = undefined;
		}
	}
	if ( fn === false ) {
		fn = returnFalse;
	} else if ( !fn ) {
		return elem;
	}

	if ( one === 1 ) {
		origFn = fn;
		fn = function( event ) {

			// Can use an empty set, since event contains the info
			jQuery().off( event );
			return origFn.apply( this, arguments );
		};

		// Use same guid so caller can remove using origFn
		fn.guid = origFn.guid || ( origFn.guid = jQuery.guid++ );
	}
	return elem.each( function() {
		jQuery.event.add( this, types, fn, data, selector );
	} );
}

/*
 * Helper functions for managing events -- not part of the public interface.
 * Props to Dean Edwards' addEvent library for many of the ideas.
 */
jQuery.event = {

	global: {},

	add: function( elem, types, handler, data, selector ) {

		var handleObjIn, eventHandle, tmp,
			events, t, handleObj,
			special, handlers, type, namespaces, origType,
			elemData = dataPriv.get( elem );

		// Don't attach events to noData or text/comment nodes (but allow plain objects)
		if ( !elemData ) {
			return;
		}

		// Caller can pass in an object of custom data in lieu of the handler
		if ( handler.handler ) {
			handleObjIn = handler;
			handler = handleObjIn.handler;
			selector = handleObjIn.selector;
		}

		// Ensure that invalid selectors throw exceptions at attach time
		// Evaluate against documentElement in case elem is a non-element node (e.g., document)
		if ( selector ) {
			jQuery.find.matchesSelector( documentElement, selector );
		}

		// Make sure that the handler has a unique ID, used to find/remove it later
		if ( !handler.guid ) {
			handler.guid = jQuery.guid++;
		}

		// Init the element's event structure and main handler, if this is the first
		if ( !( events = elemData.events ) ) {
			events = elemData.events = {};
		}
		if ( !( eventHandle = elemData.handle ) ) {
			eventHandle = elemData.handle = function( e ) {

				// Discard the second event of a jQuery.event.trigger() and
				// when an event is called after a page has unloaded
				return typeof jQuery !== "undefined" && jQuery.event.triggered !== e.type ?
					jQuery.event.dispatch.apply( elem, arguments ) : undefined;
			};
		}

		// Handle multiple events separated by a space
		types = ( types || "" ).match( rnothtmlwhite ) || [ "" ];
		t = types.length;
		while ( t-- ) {
			tmp = rtypenamespace.exec( types[ t ] ) || [];
			type = origType = tmp[ 1 ];
			namespaces = ( tmp[ 2 ] || "" ).split( "." ).sort();

			// There *must* be a type, no attaching namespace-only handlers
			if ( !type ) {
				continue;
			}

			// If event changes its type, use the special event handlers for the changed type
			special = jQuery.event.special[ type ] || {};

			// If selector defined, determine special event api type, otherwise given type
			type = ( selector ? special.delegateType : special.bindType ) || type;

			// Update special based on newly reset type
			special = jQuery.event.special[ type ] || {};

			// handleObj is passed to all event handlers
			handleObj = jQuery.extend( {
				type: type,
				origType: origType,
				data: data,
				handler: handler,
				guid: handler.guid,
				selector: selector,
				needsContext: selector && jQuery.expr.match.needsContext.test( selector ),
				namespace: namespaces.join( "." )
			}, handleObjIn );

			// Init the event handler queue if we're the first
			if ( !( handlers = events[ type ] ) ) {
				handlers = events[ type ] = [];
				handlers.delegateCount = 0;

				// Only use addEventListener if the special events handler returns false
				if ( !special.setup ||
					special.setup.call( elem, data, namespaces, eventHandle ) === false ) {

					if ( elem.addEventListener ) {
						elem.addEventListener( type, eventHandle );
					}
				}
			}

			if ( special.add ) {
				special.add.call( elem, handleObj );

				if ( !handleObj.handler.guid ) {
					handleObj.handler.guid = handler.guid;
				}
			}

			// Add to the element's handler list, delegates in front
			if ( selector ) {
				handlers.splice( handlers.delegateCount++, 0, handleObj );
			} else {
				handlers.push( handleObj );
			}

			// Keep track of which events have ever been used, for event optimization
			jQuery.event.global[ type ] = true;
		}

	},

	// Detach an event or set of events from an element
	remove: function( elem, types, handler, selector, mappedTypes ) {

		var j, origCount, tmp,
			events, t, handleObj,
			special, handlers, type, namespaces, origType,
			elemData = dataPriv.hasData( elem ) && dataPriv.get( elem );

		if ( !elemData || !( events = elemData.events ) ) {
			return;
		}

		// Once for each type.namespace in types; type may be omitted
		types = ( types || "" ).match( rnothtmlwhite ) || [ "" ];
		t = types.length;
		while ( t-- ) {
			tmp = rtypenamespace.exec( types[ t ] ) || [];
			type = origType = tmp[ 1 ];
			namespaces = ( tmp[ 2 ] || "" ).split( "." ).sort();

			// Unbind all events (on this namespace, if provided) for the element
			if ( !type ) {
				for ( type in events ) {
					jQuery.event.remove( elem, type + types[ t ], handler, selector, true );
				}
				continue;
			}

			special = jQuery.event.special[ type ] || {};
			type = ( selector ? special.delegateType : special.bindType ) || type;
			handlers = events[ type ] || [];
			tmp = tmp[ 2 ] &&
				new RegExp( "(^|\\.)" + namespaces.join( "\\.(?:.*\\.|)" ) + "(\\.|$)" );

			// Remove matching events
			origCount = j = handlers.length;
			while ( j-- ) {
				handleObj = handlers[ j ];

				if ( ( mappedTypes || origType === handleObj.origType ) &&
					( !handler || handler.guid === handleObj.guid ) &&
					( !tmp || tmp.test( handleObj.namespace ) ) &&
					( !selector || selector === handleObj.selector ||
						selector === "**" && handleObj.selector ) ) {
					handlers.splice( j, 1 );

					if ( handleObj.selector ) {
						handlers.delegateCount--;
					}
					if ( special.remove ) {
						special.remove.call( elem, handleObj );
					}
				}
			}

			// Remove generic event handler if we removed something and no more handlers exist
			// (avoids potential for endless recursion during removal of special event handlers)
			if ( origCount && !handlers.length ) {
				if ( !special.teardown ||
					special.teardown.call( elem, namespaces, elemData.handle ) === false ) {

					jQuery.removeEvent( elem, type, elemData.handle );
				}

				delete events[ type ];
			}
		}

		// Remove data and the expando if it's no longer used
		if ( jQuery.isEmptyObject( events ) ) {
			dataPriv.remove( elem, "handle events" );
		}
	},

	dispatch: function( nativeEvent ) {

		// Make a writable jQuery.Event from the native event object
		var event = jQuery.event.fix( nativeEvent );

		var i, j, ret, matched, handleObj, handlerQueue,
			args = new Array( arguments.length ),
			handlers = ( dataPriv.get( this, "events" ) || {} )[ event.type ] || [],
			special = jQuery.event.special[ event.type ] || {};

		// Use the fix-ed jQuery.Event rather than the (read-only) native event
		args[ 0 ] = event;

		for ( i = 1; i < arguments.length; i++ ) {
			args[ i ] = arguments[ i ];
		}

		event.delegateTarget = this;

		// Call the preDispatch hook for the mapped type, and let it bail if desired
		if ( special.preDispatch && special.preDispatch.call( this, event ) === false ) {
			return;
		}

		// Determine handlers
		handlerQueue = jQuery.event.handlers.call( this, event, handlers );

		// Run delegates first; they may want to stop propagation beneath us
		i = 0;
		while ( ( matched = handlerQueue[ i++ ] ) && !event.isPropagationStopped() ) {
			event.currentTarget = matched.elem;

			j = 0;
			while ( ( handleObj = matched.handlers[ j++ ] ) &&
				!event.isImmediatePropagationStopped() ) {

				// Triggered event must either 1) have no namespace, or 2) have namespace(s)
				// a subset or equal to those in the bound event (both can have no namespace).
				if ( !event.rnamespace || event.rnamespace.test( handleObj.namespace ) ) {

					event.handleObj = handleObj;
					event.data = handleObj.data;

					ret = ( ( jQuery.event.special[ handleObj.origType ] || {} ).handle ||
						handleObj.handler ).apply( matched.elem, args );

					if ( ret !== undefined ) {
						if ( ( event.result = ret ) === false ) {
							event.preventDefault();
							event.stopPropagation();
						}
					}
				}
			}
		}

		// Call the postDispatch hook for the mapped type
		if ( special.postDispatch ) {
			special.postDispatch.call( this, event );
		}

		return event.result;
	},

	handlers: function( event, handlers ) {
		var i, handleObj, sel, matchedHandlers, matchedSelectors,
			handlerQueue = [],
			delegateCount = handlers.delegateCount,
			cur = event.target;

		// Find delegate handlers
		if ( delegateCount &&

			// Support: IE <=9
			// Black-hole SVG <use> instance trees (trac-13180)
			cur.nodeType &&

			// Support: Firefox <=42
			// Suppress spec-violating clicks indicating a non-primary pointer button (trac-3861)
			// https://www.w3.org/TR/DOM-Level-3-Events/#event-type-click
			// Support: IE 11 only
			// ...but not arrow key "clicks" of radio inputs, which can have `button` -1 (gh-2343)
			!( event.type === "click" && event.button >= 1 ) ) {

			for ( ; cur !== this; cur = cur.parentNode || this ) {

				// Don't check non-elements (#13208)
				// Don't process clicks on disabled elements (#6911, #8165, #11382, #11764)
				if ( cur.nodeType === 1 && !( event.type === "click" && cur.disabled === true ) ) {
					matchedHandlers = [];
					matchedSelectors = {};
					for ( i = 0; i < delegateCount; i++ ) {
						handleObj = handlers[ i ];

						// Don't conflict with Object.prototype properties (#13203)
						sel = handleObj.selector + " ";

						if ( matchedSelectors[ sel ] === undefined ) {
							matchedSelectors[ sel ] = handleObj.needsContext ?
								jQuery( sel, this ).index( cur ) > -1 :
								jQuery.find( sel, this, null, [ cur ] ).length;
						}
						if ( matchedSelectors[ sel ] ) {
							matchedHandlers.push( handleObj );
						}
					}
					if ( matchedHandlers.length ) {
						handlerQueue.push( { elem: cur, handlers: matchedHandlers } );
					}
				}
			}
		}

		// Add the remaining (directly-bound) handlers
		cur = this;
		if ( delegateCount < handlers.length ) {
			handlerQueue.push( { elem: cur, handlers: handlers.slice( delegateCount ) } );
		}

		return handlerQueue;
	},

	addProp: function( name, hook ) {
		Object.defineProperty( jQuery.Event.prototype, name, {
			enumerable: true,
			configurable: true,

			get: jQuery.isFunction( hook ) ?
				function() {
					if ( this.originalEvent ) {
							return hook( this.originalEvent );
					}
				} :
				function() {
					if ( this.originalEvent ) {
							return this.originalEvent[ name ];
					}
				},

			set: function( value ) {
				Object.defineProperty( this, name, {
					enumerable: true,
					configurable: true,
					writable: true,
					value: value
				} );
			}
		} );
	},

	fix: function( originalEvent ) {
		return originalEvent[ jQuery.expando ] ?
			originalEvent :
			new jQuery.Event( originalEvent );
	},

	special: {
		load: {

			// Prevent triggered image.load events from bubbling to window.load
			noBubble: true
		},
		focus: {

			// Fire native event if possible so blur/focus sequence is correct
			trigger: function() {
				if ( this !== safeActiveElement() && this.focus ) {
					this.focus();
					return false;
				}
			},
			delegateType: "focusin"
		},
		blur: {
			trigger: function() {
				if ( this === safeActiveElement() && this.blur ) {
					this.blur();
					return false;
				}
			},
			delegateType: "focusout"
		},
		click: {

			// For checkbox, fire native event so checked state will be right
			trigger: function() {
				if ( this.type === "checkbox" && this.click && nodeName( this, "input" ) ) {
					this.click();
					return false;
				}
			},

			// For cross-browser consistency, don't fire native .click() on links
			_default: function( event ) {
				return nodeName( event.target, "a" );
			}
		},

		beforeunload: {
			postDispatch: function( event ) {

				// Support: Firefox 20+
				// Firefox doesn't alert if the returnValue field is not set.
				if ( event.result !== undefined && event.originalEvent ) {
					event.originalEvent.returnValue = event.result;
				}
			}
		}
	}
};

jQuery.removeEvent = function( elem, type, handle ) {

	// This "if" is needed for plain objects
	if ( elem.removeEventListener ) {
		elem.removeEventListener( type, handle );
	}
};

jQuery.Event = function( src, props ) {

	// Allow instantiation without the 'new' keyword
	if ( !( this instanceof jQuery.Event ) ) {
		return new jQuery.Event( src, props );
	}

	// Event object
	if ( src && src.type ) {
		this.originalEvent = src;
		this.type = src.type;

		// Events bubbling up the document may have been marked as prevented
		// by a handler lower down the tree; reflect the correct value.
		this.isDefaultPrevented = src.defaultPrevented ||
				src.defaultPrevented === undefined &&

				// Support: Android <=2.3 only
				src.returnValue === false ?
			returnTrue :
			returnFalse;

		// Create target properties
		// Support: Safari <=6 - 7 only
		// Target should not be a text node (#504, #13143)
		this.target = ( src.target && src.target.nodeType === 3 ) ?
			src.target.parentNode :
			src.target;

		this.currentTarget = src.currentTarget;
		this.relatedTarget = src.relatedTarget;

	// Event type
	} else {
		this.type = src;
	}

	// Put explicitly provided properties onto the event object
	if ( props ) {
		jQuery.extend( this, props );
	}

	// Create a timestamp if incoming event doesn't have one
	this.timeStamp = src && src.timeStamp || jQuery.now();

	// Mark it as fixed
	this[ jQuery.expando ] = true;
};

// jQuery.Event is based on DOM3 Events as specified by the ECMAScript Language Binding
// https://www.w3.org/TR/2003/WD-DOM-Level-3-Events-20030331/ecma-script-binding.html
jQuery.Event.prototype = {
	constructor: jQuery.Event,
	isDefaultPrevented: returnFalse,
	isPropagationStopped: returnFalse,
	isImmediatePropagationStopped: returnFalse,
	isSimulated: false,

	preventDefault: function() {
		var e = this.originalEvent;

		this.isDefaultPrevented = returnTrue;

		if ( e && !this.isSimulated ) {
			e.preventDefault();
		}
	},
	stopPropagation: function() {
		var e = this.originalEvent;

		this.isPropagationStopped = returnTrue;

		if ( e && !this.isSimulated ) {
			e.stopPropagation();
		}
	},
	stopImmediatePropagation: function() {
		var e = this.originalEvent;

		this.isImmediatePropagationStopped = returnTrue;

		if ( e && !this.isSimulated ) {
			e.stopImmediatePropagation();
		}

		this.stopPropagation();
	}
};

// Includes all common event props including KeyEvent and MouseEvent specific props
jQuery.each( {
	altKey: true,
	bubbles: true,
	cancelable: true,
	changedTouches: true,
	ctrlKey: true,
	detail: true,
	eventPhase: true,
	metaKey: true,
	pageX: true,
	pageY: true,
	shiftKey: true,
	view: true,
	"char": true,
	charCode: true,
	key: true,
	keyCode: true,
	button: true,
	buttons: true,
	clientX: true,
	clientY: true,
	offsetX: true,
	offsetY: true,
	pointerId: true,
	pointerType: true,
	screenX: true,
	screenY: true,
	targetTouches: true,
	toElement: true,
	touches: true,

	which: function( event ) {
		var button = event.button;

		// Add which for key events
		if ( event.which == null && rkeyEvent.test( event.type ) ) {
			return event.charCode != null ? event.charCode : event.keyCode;
		}

		// Add which for click: 1 === left; 2 === middle; 3 === right
		if ( !event.which && button !== undefined && rmouseEvent.test( event.type ) ) {
			if ( button & 1 ) {
				return 1;
			}

			if ( button & 2 ) {
				return 3;
			}

			if ( button & 4 ) {
				return 2;
			}

			return 0;
		}

		return event.which;
	}
}, jQuery.event.addProp );

// Create mouseenter/leave events using mouseover/out and event-time checks
// so that event delegation works in jQuery.
// Do the same for pointerenter/pointerleave and pointerover/pointerout
//
// Support: Safari 7 only
// Safari sends mouseenter too often; see:
// https://bugs.chromium.org/p/chromium/issues/detail?id=470258
// for the description of the bug (it existed in older Chrome versions as well).
jQuery.each( {
	mouseenter: "mouseover",
	mouseleave: "mouseout",
	pointerenter: "pointerover",
	pointerleave: "pointerout"
}, function( orig, fix ) {
	jQuery.event.special[ orig ] = {
		delegateType: fix,
		bindType: fix,

		handle: function( event ) {
			var ret,
				target = this,
				related = event.relatedTarget,
				handleObj = event.handleObj;

			// For mouseenter/leave call the handler if related is outside the target.
			// NB: No relatedTarget if the mouse left/entered the browser window
			if ( !related || ( related !== target && !jQuery.contains( target, related ) ) ) {
				event.type = handleObj.origType;
				ret = handleObj.handler.apply( this, arguments );
				event.type = fix;
			}
			return ret;
		}
	};
} );

jQuery.fn.extend( {

	on: function( types, selector, data, fn ) {
		return on( this, types, selector, data, fn );
	},
	one: function( types, selector, data, fn ) {
		return on( this, types, selector, data, fn, 1 );
	},
	off: function( types, selector, fn ) {
		var handleObj, type;
		if ( types && types.preventDefault && types.handleObj ) {

			// ( event )  dispatched jQuery.Event
			handleObj = types.handleObj;
			jQuery( types.delegateTarget ).off(
				handleObj.namespace ?
					handleObj.origType + "." + handleObj.namespace :
					handleObj.origType,
				handleObj.selector,
				handleObj.handler
			);
			return this;
		}
		if ( typeof types === "object" ) {

			// ( types-object [, selector] )
			for ( type in types ) {
				this.off( type, selector, types[ type ] );
			}
			return this;
		}
		if ( selector === false || typeof selector === "function" ) {

			// ( types [, fn] )
			fn = selector;
			selector = undefined;
		}
		if ( fn === false ) {
			fn = returnFalse;
		}
		return this.each( function() {
			jQuery.event.remove( this, types, fn, selector );
		} );
	}
} );


var

	/* eslint-disable max-len */

	// See https://github.com/eslint/eslint/issues/3229
	rxhtmlTag = /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([a-z][^\/\0>\x20\t\r\n\f]*)[^>]*)\/>/gi,

	/* eslint-enable */

	// Support: IE <=10 - 11, Edge 12 - 13
	// In IE/Edge using regex groups here causes severe slowdowns.
	// See https://connect.microsoft.com/IE/feedback/details/1736512/
	rnoInnerhtml = /<script|<style|<link/i,

	// checked="checked" or checked
	rchecked = /checked\s*(?:[^=]|=\s*.checked.)/i,
	rscriptTypeMasked = /^true\/(.*)/,
	rcleanScript = /^\s*<!(?:\[CDATA\[|--)|(?:\]\]|--)>\s*$/g;

// Prefer a tbody over its parent table for containing new rows
function manipulationTarget( elem, content ) {
	if ( nodeName( elem, "table" ) &&
		nodeName( content.nodeType !== 11 ? content : content.firstChild, "tr" ) ) {

		return jQuery( ">tbody", elem )[ 0 ] || elem;
	}

	return elem;
}

// Replace/restore the type attribute of script elements for safe DOM manipulation
function disableScript( elem ) {
	elem.type = ( elem.getAttribute( "type" ) !== null ) + "/" + elem.type;
	return elem;
}
function restoreScript( elem ) {
	var match = rscriptTypeMasked.exec( elem.type );

	if ( match ) {
		elem.type = match[ 1 ];
	} else {
		elem.removeAttribute( "type" );
	}

	return elem;
}

function cloneCopyEvent( src, dest ) {
	var i, l, type, pdataOld, pdataCur, udataOld, udataCur, events;

	if ( dest.nodeType !== 1 ) {
		return;
	}

	// 1. Copy private data: events, handlers, etc.
	if ( dataPriv.hasData( src ) ) {
		pdataOld = dataPriv.access( src );
		pdataCur = dataPriv.set( dest, pdataOld );
		events = pdataOld.events;

		if ( events ) {
			delete pdataCur.handle;
			pdataCur.events = {};

			for ( type in events ) {
				for ( i = 0, l = events[ type ].length; i < l; i++ ) {
					jQuery.event.add( dest, type, events[ type ][ i ] );
				}
			}
		}
	}

	// 2. Copy user data
	if ( dataUser.hasData( src ) ) {
		udataOld = dataUser.access( src );
		udataCur = jQuery.extend( {}, udataOld );

		dataUser.set( dest, udataCur );
	}
}

// Fix IE bugs, see support tests
function fixInput( src, dest ) {
	var nodeName = dest.nodeName.toLowerCase();

	// Fails to persist the checked state of a cloned checkbox or radio button.
	if ( nodeName === "input" && rcheckableType.test( src.type ) ) {
		dest.checked = src.checked;

	// Fails to return the selected option to the default selected state when cloning options
	} else if ( nodeName === "input" || nodeName === "textarea" ) {
		dest.defaultValue = src.defaultValue;
	}
}

function domManip( collection, args, callback, ignored ) {

	// Flatten any nested arrays
	args = concat.apply( [], args );

	var fragment, first, scripts, hasScripts, node, doc,
		i = 0,
		l = collection.length,
		iNoClone = l - 1,
		value = args[ 0 ],
		isFunction = jQuery.isFunction( value );

	// We can't cloneNode fragments that contain checked, in WebKit
	if ( isFunction ||
			( l > 1 && typeof value === "string" &&
				!support.checkClone && rchecked.test( value ) ) ) {
		return collection.each( function( index ) {
			var self = collection.eq( index );
			if ( isFunction ) {
				args[ 0 ] = value.call( this, index, self.html() );
			}
			domManip( self, args, callback, ignored );
		} );
	}

	if ( l ) {
		fragment = buildFragment( args, collection[ 0 ].ownerDocument, false, collection, ignored );
		first = fragment.firstChild;

		if ( fragment.childNodes.length === 1 ) {
			fragment = first;
		}

		// Require either new content or an interest in ignored elements to invoke the callback
		if ( first || ignored ) {
			scripts = jQuery.map( getAll( fragment, "script" ), disableScript );
			hasScripts = scripts.length;

			// Use the original fragment for the last item
			// instead of the first because it can end up
			// being emptied incorrectly in certain situations (#8070).
			for ( ; i < l; i++ ) {
				node = fragment;

				if ( i !== iNoClone ) {
					node = jQuery.clone( node, true, true );

					// Keep references to cloned scripts for later restoration
					if ( hasScripts ) {

						// Support: Android <=4.0 only, PhantomJS 1 only
						// push.apply(_, arraylike) throws on ancient WebKit
						jQuery.merge( scripts, getAll( node, "script" ) );
					}
				}

				callback.call( collection[ i ], node, i );
			}

			if ( hasScripts ) {
				doc = scripts[ scripts.length - 1 ].ownerDocument;

				// Reenable scripts
				jQuery.map( scripts, restoreScript );

				// Evaluate executable scripts on first document insertion
				for ( i = 0; i < hasScripts; i++ ) {
					node = scripts[ i ];
					if ( rscriptType.test( node.type || "" ) &&
						!dataPriv.access( node, "globalEval" ) &&
						jQuery.contains( doc, node ) ) {

						if ( node.src ) {

							// Optional AJAX dependency, but won't run scripts if not present
							if ( jQuery._evalUrl ) {
								jQuery._evalUrl( node.src );
							}
						} else {
							DOMEval( node.textContent.replace( rcleanScript, "" ), doc );
						}
					}
				}
			}
		}
	}

	return collection;
}

function remove( elem, selector, keepData ) {
	var node,
		nodes = selector ? jQuery.filter( selector, elem ) : elem,
		i = 0;

	for ( ; ( node = nodes[ i ] ) != null; i++ ) {
		if ( !keepData && node.nodeType === 1 ) {
			jQuery.cleanData( getAll( node ) );
		}

		if ( node.parentNode ) {
			if ( keepData && jQuery.contains( node.ownerDocument, node ) ) {
				setGlobalEval( getAll( node, "script" ) );
			}
			node.parentNode.removeChild( node );
		}
	}

	return elem;
}

jQuery.extend( {
	htmlPrefilter: function( html ) {
		return html.replace( rxhtmlTag, "<$1></$2>" );
	},

	clone: function( elem, dataAndEvents, deepDataAndEvents ) {
		var i, l, srcElements, destElements,
			clone = elem.cloneNode( true ),
			inPage = jQuery.contains( elem.ownerDocument, elem );

		// Fix IE cloning issues
		if ( !support.noCloneChecked && ( elem.nodeType === 1 || elem.nodeType === 11 ) &&
				!jQuery.isXMLDoc( elem ) ) {

			// We eschew Sizzle here for performance reasons: https://jsperf.com/getall-vs-sizzle/2
			destElements = getAll( clone );
			srcElements = getAll( elem );

			for ( i = 0, l = srcElements.length; i < l; i++ ) {
				fixInput( srcElements[ i ], destElements[ i ] );
			}
		}

		// Copy the events from the original to the clone
		if ( dataAndEvents ) {
			if ( deepDataAndEvents ) {
				srcElements = srcElements || getAll( elem );
				destElements = destElements || getAll( clone );

				for ( i = 0, l = srcElements.length; i < l; i++ ) {
					cloneCopyEvent( srcElements[ i ], destElements[ i ] );
				}
			} else {
				cloneCopyEvent( elem, clone );
			}
		}

		// Preserve script evaluation history
		destElements = getAll( clone, "script" );
		if ( destElements.length > 0 ) {
			setGlobalEval( destElements, !inPage && getAll( elem, "script" ) );
		}

		// Return the cloned set
		return clone;
	},

	cleanData: function( elems ) {
		var data, elem, type,
			special = jQuery.event.special,
			i = 0;

		for ( ; ( elem = elems[ i ] ) !== undefined; i++ ) {
			if ( acceptData( elem ) ) {
				if ( ( data = elem[ dataPriv.expando ] ) ) {
					if ( data.events ) {
						for ( type in data.events ) {
							if ( special[ type ] ) {
								jQuery.event.remove( elem, type );

							// This is a shortcut to avoid jQuery.event.remove's overhead
							} else {
								jQuery.removeEvent( elem, type, data.handle );
							}
						}
					}

					// Support: Chrome <=35 - 45+
					// Assign undefined instead of using delete, see Data#remove
					elem[ dataPriv.expando ] = undefined;
				}
				if ( elem[ dataUser.expando ] ) {

					// Support: Chrome <=35 - 45+
					// Assign undefined instead of using delete, see Data#remove
					elem[ dataUser.expando ] = undefined;
				}
			}
		}
	}
} );

jQuery.fn.extend( {
	detach: function( selector ) {
		return remove( this, selector, true );
	},

	remove: function( selector ) {
		return remove( this, selector );
	},

	text: function( value ) {
		return access( this, function( value ) {
			return value === undefined ?
				jQuery.text( this ) :
				this.empty().each( function() {
					if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
						this.textContent = value;
					}
				} );
		}, null, value, arguments.length );
	},

	append: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
				var target = manipulationTarget( this, elem );
				target.appendChild( elem );
			}
		} );
	},

	prepend: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
				var target = manipulationTarget( this, elem );
				target.insertBefore( elem, target.firstChild );
			}
		} );
	},

	before: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.parentNode ) {
				this.parentNode.insertBefore( elem, this );
			}
		} );
	},

	after: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.parentNode ) {
				this.parentNode.insertBefore( elem, this.nextSibling );
			}
		} );
	},

	empty: function() {
		var elem,
			i = 0;

		for ( ; ( elem = this[ i ] ) != null; i++ ) {
			if ( elem.nodeType === 1 ) {

				// Prevent memory leaks
				jQuery.cleanData( getAll( elem, false ) );

				// Remove any remaining nodes
				elem.textContent = "";
			}
		}

		return this;
	},

	clone: function( dataAndEvents, deepDataAndEvents ) {
		dataAndEvents = dataAndEvents == null ? false : dataAndEvents;
		deepDataAndEvents = deepDataAndEvents == null ? dataAndEvents : deepDataAndEvents;

		return this.map( function() {
			return jQuery.clone( this, dataAndEvents, deepDataAndEvents );
		} );
	},

	html: function( value ) {
		return access( this, function( value ) {
			var elem = this[ 0 ] || {},
				i = 0,
				l = this.length;

			if ( value === undefined && elem.nodeType === 1 ) {
				return elem.innerHTML;
			}

			// See if we can take a shortcut and just use innerHTML
			if ( typeof value === "string" && !rnoInnerhtml.test( value ) &&
				!wrapMap[ ( rtagName.exec( value ) || [ "", "" ] )[ 1 ].toLowerCase() ] ) {

				value = jQuery.htmlPrefilter( value );

				try {
					for ( ; i < l; i++ ) {
						elem = this[ i ] || {};

						// Remove element nodes and prevent memory leaks
						if ( elem.nodeType === 1 ) {
							jQuery.cleanData( getAll( elem, false ) );
							elem.innerHTML = value;
						}
					}

					elem = 0;

				// If using innerHTML throws an exception, use the fallback method
				} catch ( e ) {}
			}

			if ( elem ) {
				this.empty().append( value );
			}
		}, null, value, arguments.length );
	},

	replaceWith: function() {
		var ignored = [];

		// Make the changes, replacing each non-ignored context element with the new content
		return domManip( this, arguments, function( elem ) {
			var parent = this.parentNode;

			if ( jQuery.inArray( this, ignored ) < 0 ) {
				jQuery.cleanData( getAll( this ) );
				if ( parent ) {
					parent.replaceChild( elem, this );
				}
			}

		// Force callback invocation
		}, ignored );
	}
} );

jQuery.each( {
	appendTo: "append",
	prependTo: "prepend",
	insertBefore: "before",
	insertAfter: "after",
	replaceAll: "replaceWith"
}, function( name, original ) {
	jQuery.fn[ name ] = function( selector ) {
		var elems,
			ret = [],
			insert = jQuery( selector ),
			last = insert.length - 1,
			i = 0;

		for ( ; i <= last; i++ ) {
			elems = i === last ? this : this.clone( true );
			jQuery( insert[ i ] )[ original ]( elems );

			// Support: Android <=4.0 only, PhantomJS 1 only
			// .get() because push.apply(_, arraylike) throws on ancient WebKit
			push.apply( ret, elems.get() );
		}

		return this.pushStack( ret );
	};
} );
var rmargin = ( /^margin/ );

var rnumnonpx = new RegExp( "^(" + pnum + ")(?!px)[a-z%]+$", "i" );

var getStyles = function( elem ) {

		// Support: IE <=11 only, Firefox <=30 (#15098, #14150)
		// IE throws on elements created in popups
		// FF meanwhile throws on frame elements through "defaultView.getComputedStyle"
		var view = elem.ownerDocument.defaultView;

		if ( !view || !view.opener ) {
			view = window;
		}

		return view.getComputedStyle( elem );
	};



( function() {

	// Executing both pixelPosition & boxSizingReliable tests require only one layout
	// so they're executed at the same time to save the second computation.
	function computeStyleTests() {

		// This is a singleton, we need to execute it only once
		if ( !div ) {
			return;
		}

		div.style.cssText =
			"box-sizing:border-box;" +
			"position:relative;display:block;" +
			"margin:auto;border:1px;padding:1px;" +
			"top:1%;width:50%";
		div.innerHTML = "";
		documentElement.appendChild( container );

		var divStyle = window.getComputedStyle( div );
		pixelPositionVal = divStyle.top !== "1%";

		// Support: Android 4.0 - 4.3 only, Firefox <=3 - 44
		reliableMarginLeftVal = divStyle.marginLeft === "2px";
		boxSizingReliableVal = divStyle.width === "4px";

		// Support: Android 4.0 - 4.3 only
		// Some styles come back with percentage values, even though they shouldn't
		div.style.marginRight = "50%";
		pixelMarginRightVal = divStyle.marginRight === "4px";

		documentElement.removeChild( container );

		// Nullify the div so it wouldn't be stored in the memory and
		// it will also be a sign that checks already performed
		div = null;
	}

	var pixelPositionVal, boxSizingReliableVal, pixelMarginRightVal, reliableMarginLeftVal,
		container = document.createElement( "div" ),
		div = document.createElement( "div" );

	// Finish early in limited (non-browser) environments
	if ( !div.style ) {
		return;
	}

	// Support: IE <=9 - 11 only
	// Style of cloned element affects source element cloned (#8908)
	div.style.backgroundClip = "content-box";
	div.cloneNode( true ).style.backgroundClip = "";
	support.clearCloneStyle = div.style.backgroundClip === "content-box";

	container.style.cssText = "border:0;width:8px;height:0;top:0;left:-9999px;" +
		"padding:0;margin-top:1px;position:absolute";
	container.appendChild( div );

	jQuery.extend( support, {
		pixelPosition: function() {
			computeStyleTests();
			return pixelPositionVal;
		},
		boxSizingReliable: function() {
			computeStyleTests();
			return boxSizingReliableVal;
		},
		pixelMarginRight: function() {
			computeStyleTests();
			return pixelMarginRightVal;
		},
		reliableMarginLeft: function() {
			computeStyleTests();
			return reliableMarginLeftVal;
		}
	} );
} )();


function curCSS( elem, name, computed ) {
	var width, minWidth, maxWidth, ret,

		// Support: Firefox 51+
		// Retrieving style before computed somehow
		// fixes an issue with getting wrong values
		// on detached elements
		style = elem.style;

	computed = computed || getStyles( elem );

	// getPropertyValue is needed for:
	//   .css('filter') (IE 9 only, #12537)
	//   .css('--customProperty) (#3144)
	if ( computed ) {
		ret = computed.getPropertyValue( name ) || computed[ name ];

		if ( ret === "" && !jQuery.contains( elem.ownerDocument, elem ) ) {
			ret = jQuery.style( elem, name );
		}

		// A tribute to the "awesome hack by Dean Edwards"
		// Android Browser returns percentage for some values,
		// but width seems to be reliably pixels.
		// This is against the CSSOM draft spec:
		// https://drafts.csswg.org/cssom/#resolved-values
		if ( !support.pixelMarginRight() && rnumnonpx.test( ret ) && rmargin.test( name ) ) {

			// Remember the original values
			width = style.width;
			minWidth = style.minWidth;
			maxWidth = style.maxWidth;

			// Put in the new values to get a computed value out
			style.minWidth = style.maxWidth = style.width = ret;
			ret = computed.width;

			// Revert the changed values
			style.width = width;
			style.minWidth = minWidth;
			style.maxWidth = maxWidth;
		}
	}

	return ret !== undefined ?

		// Support: IE <=9 - 11 only
		// IE returns zIndex value as an integer.
		ret + "" :
		ret;
}


function addGetHookIf( conditionFn, hookFn ) {

	// Define the hook, we'll check on the first run if it's really needed.
	return {
		get: function() {
			if ( conditionFn() ) {

				// Hook not needed (or it's not possible to use it due
				// to missing dependency), remove it.
				delete this.get;
				return;
			}

			// Hook needed; redefine it so that the support test is not executed again.
			return ( this.get = hookFn ).apply( this, arguments );
		}
	};
}


var

	// Swappable if display is none or starts with table
	// except "table", "table-cell", or "table-caption"
	// See here for display values: https://developer.mozilla.org/en-US/docs/CSS/display
	rdisplayswap = /^(none|table(?!-c[ea]).+)/,
	rcustomProp = /^--/,
	cssShow = { position: "absolute", visibility: "hidden", display: "block" },
	cssNormalTransform = {
		letterSpacing: "0",
		fontWeight: "400"
	},

	cssPrefixes = [ "Webkit", "Moz", "ms" ],
	emptyStyle = document.createElement( "div" ).style;

// Return a css property mapped to a potentially vendor prefixed property
function vendorPropName( name ) {

	// Shortcut for names that are not vendor prefixed
	if ( name in emptyStyle ) {
		return name;
	}

	// Check for vendor prefixed names
	var capName = name[ 0 ].toUpperCase() + name.slice( 1 ),
		i = cssPrefixes.length;

	while ( i-- ) {
		name = cssPrefixes[ i ] + capName;
		if ( name in emptyStyle ) {
			return name;
		}
	}
}

// Return a property mapped along what jQuery.cssProps suggests or to
// a vendor prefixed property.
function finalPropName( name ) {
	var ret = jQuery.cssProps[ name ];
	if ( !ret ) {
		ret = jQuery.cssProps[ name ] = vendorPropName( name ) || name;
	}
	return ret;
}

function setPositiveNumber( elem, value, subtract ) {

	// Any relative (+/-) values have already been
	// normalized at this point
	var matches = rcssNum.exec( value );
	return matches ?

		// Guard against undefined "subtract", e.g., when used as in cssHooks
		Math.max( 0, matches[ 2 ] - ( subtract || 0 ) ) + ( matches[ 3 ] || "px" ) :
		value;
}

function augmentWidthOrHeight( elem, name, extra, isBorderBox, styles ) {
	var i,
		val = 0;

	// If we already have the right measurement, avoid augmentation
	if ( extra === ( isBorderBox ? "border" : "content" ) ) {
		i = 4;

	// Otherwise initialize for horizontal or vertical properties
	} else {
		i = name === "width" ? 1 : 0;
	}

	for ( ; i < 4; i += 2 ) {

		// Both box models exclude margin, so add it if we want it
		if ( extra === "margin" ) {
			val += jQuery.css( elem, extra + cssExpand[ i ], true, styles );
		}

		if ( isBorderBox ) {

			// border-box includes padding, so remove it if we want content
			if ( extra === "content" ) {
				val -= jQuery.css( elem, "padding" + cssExpand[ i ], true, styles );
			}

			// At this point, extra isn't border nor margin, so remove border
			if ( extra !== "margin" ) {
				val -= jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );
			}
		} else {

			// At this point, extra isn't content, so add padding
			val += jQuery.css( elem, "padding" + cssExpand[ i ], true, styles );

			// At this point, extra isn't content nor padding, so add border
			if ( extra !== "padding" ) {
				val += jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );
			}
		}
	}

	return val;
}

function getWidthOrHeight( elem, name, extra ) {

	// Start with computed style
	var valueIsBorderBox,
		styles = getStyles( elem ),
		val = curCSS( elem, name, styles ),
		isBorderBox = jQuery.css( elem, "boxSizing", false, styles ) === "border-box";

	// Computed unit is not pixels. Stop here and return.
	if ( rnumnonpx.test( val ) ) {
		return val;
	}

	// Check for style in case a browser which returns unreliable values
	// for getComputedStyle silently falls back to the reliable elem.style
	valueIsBorderBox = isBorderBox &&
		( support.boxSizingReliable() || val === elem.style[ name ] );

	// Fall back to offsetWidth/Height when value is "auto"
	// This happens for inline elements with no explicit setting (gh-3571)
	if ( val === "auto" ) {
		val = elem[ "offset" + name[ 0 ].toUpperCase() + name.slice( 1 ) ];
	}

	// Normalize "", auto, and prepare for extra
	val = parseFloat( val ) || 0;

	// Use the active box-sizing model to add/subtract irrelevant styles
	return ( val +
		augmentWidthOrHeight(
			elem,
			name,
			extra || ( isBorderBox ? "border" : "content" ),
			valueIsBorderBox,
			styles
		)
	) + "px";
}

jQuery.extend( {

	// Add in style property hooks for overriding the default
	// behavior of getting and setting a style property
	cssHooks: {
		opacity: {
			get: function( elem, computed ) {
				if ( computed ) {

					// We should always get a number back from opacity
					var ret = curCSS( elem, "opacity" );
					return ret === "" ? "1" : ret;
				}
			}
		}
	},

	// Don't automatically add "px" to these possibly-unitless properties
	cssNumber: {
		"animationIterationCount": true,
		"columnCount": true,
		"fillOpacity": true,
		"flexGrow": true,
		"flexShrink": true,
		"fontWeight": true,
		"lineHeight": true,
		"opacity": true,
		"order": true,
		"orphans": true,
		"widows": true,
		"zIndex": true,
		"zoom": true
	},

	// Add in properties whose names you wish to fix before
	// setting or getting the value
	cssProps: {
		"float": "cssFloat"
	},

	// Get and set the style property on a DOM Node
	style: function( elem, name, value, extra ) {

		// Don't set styles on text and comment nodes
		if ( !elem || elem.nodeType === 3 || elem.nodeType === 8 || !elem.style ) {
			return;
		}

		// Make sure that we're working with the right name
		var ret, type, hooks,
			origName = jQuery.camelCase( name ),
			isCustomProp = rcustomProp.test( name ),
			style = elem.style;

		// Make sure that we're working with the right name. We don't
		// want to query the value if it is a CSS custom property
		// since they are user-defined.
		if ( !isCustomProp ) {
			name = finalPropName( origName );
		}

		// Gets hook for the prefixed version, then unprefixed version
		hooks = jQuery.cssHooks[ name ] || jQuery.cssHooks[ origName ];

		// Check if we're setting a value
		if ( value !== undefined ) {
			type = typeof value;

			// Convert "+=" or "-=" to relative numbers (#7345)
			if ( type === "string" && ( ret = rcssNum.exec( value ) ) && ret[ 1 ] ) {
				value = adjustCSS( elem, name, ret );

				// Fixes bug #9237
				type = "number";
			}

			// Make sure that null and NaN values aren't set (#7116)
			if ( value == null || value !== value ) {
				return;
			}

			// If a number was passed in, add the unit (except for certain CSS properties)
			if ( type === "number" ) {
				value += ret && ret[ 3 ] || ( jQuery.cssNumber[ origName ] ? "" : "px" );
			}

			// background-* props affect original clone's values
			if ( !support.clearCloneStyle && value === "" && name.indexOf( "background" ) === 0 ) {
				style[ name ] = "inherit";
			}

			// If a hook was provided, use that value, otherwise just set the specified value
			if ( !hooks || !( "set" in hooks ) ||
				( value = hooks.set( elem, value, extra ) ) !== undefined ) {

				if ( isCustomProp ) {
					style.setProperty( name, value );
				} else {
					style[ name ] = value;
				}
			}

		} else {

			// If a hook was provided get the non-computed value from there
			if ( hooks && "get" in hooks &&
				( ret = hooks.get( elem, false, extra ) ) !== undefined ) {

				return ret;
			}

			// Otherwise just get the value from the style object
			return style[ name ];
		}
	},

	css: function( elem, name, extra, styles ) {
		var val, num, hooks,
			origName = jQuery.camelCase( name ),
			isCustomProp = rcustomProp.test( name );

		// Make sure that we're working with the right name. We don't
		// want to modify the value if it is a CSS custom property
		// since they are user-defined.
		if ( !isCustomProp ) {
			name = finalPropName( origName );
		}

		// Try prefixed name followed by the unprefixed name
		hooks = jQuery.cssHooks[ name ] || jQuery.cssHooks[ origName ];

		// If a hook was provided get the computed value from there
		if ( hooks && "get" in hooks ) {
			val = hooks.get( elem, true, extra );
		}

		// Otherwise, if a way to get the computed value exists, use that
		if ( val === undefined ) {
			val = curCSS( elem, name, styles );
		}

		// Convert "normal" to computed value
		if ( val === "normal" && name in cssNormalTransform ) {
			val = cssNormalTransform[ name ];
		}

		// Make numeric if forced or a qualifier was provided and val looks numeric
		if ( extra === "" || extra ) {
			num = parseFloat( val );
			return extra === true || isFinite( num ) ? num || 0 : val;
		}

		return val;
	}
} );

jQuery.each( [ "height", "width" ], function( i, name ) {
	jQuery.cssHooks[ name ] = {
		get: function( elem, computed, extra ) {
			if ( computed ) {

				// Certain elements can have dimension info if we invisibly show them
				// but it must have a current display style that would benefit
				return rdisplayswap.test( jQuery.css( elem, "display" ) ) &&

					// Support: Safari 8+
					// Table columns in Safari have non-zero offsetWidth & zero
					// getBoundingClientRect().width unless display is changed.
					// Support: IE <=11 only
					// Running getBoundingClientRect on a disconnected node
					// in IE throws an error.
					( !elem.getClientRects().length || !elem.getBoundingClientRect().width ) ?
						swap( elem, cssShow, function() {
							return getWidthOrHeight( elem, name, extra );
						} ) :
						getWidthOrHeight( elem, name, extra );
			}
		},

		set: function( elem, value, extra ) {
			var matches,
				styles = extra && getStyles( elem ),
				subtract = extra && augmentWidthOrHeight(
					elem,
					name,
					extra,
					jQuery.css( elem, "boxSizing", false, styles ) === "border-box",
					styles
				);

			// Convert to pixels if value adjustment is needed
			if ( subtract && ( matches = rcssNum.exec( value ) ) &&
				( matches[ 3 ] || "px" ) !== "px" ) {

				elem.style[ name ] = value;
				value = jQuery.css( elem, name );
			}

			return setPositiveNumber( elem, value, subtract );
		}
	};
} );

jQuery.cssHooks.marginLeft = addGetHookIf( support.reliableMarginLeft,
	function( elem, computed ) {
		if ( computed ) {
			return ( parseFloat( curCSS( elem, "marginLeft" ) ) ||
				elem.getBoundingClientRect().left -
					swap( elem, { marginLeft: 0 }, function() {
						return elem.getBoundingClientRect().left;
					} )
				) + "px";
		}
	}
);

// These hooks are used by animate to expand properties
jQuery.each( {
	margin: "",
	padding: "",
	border: "Width"
}, function( prefix, suffix ) {
	jQuery.cssHooks[ prefix + suffix ] = {
		expand: function( value ) {
			var i = 0,
				expanded = {},

				// Assumes a single number if not a string
				parts = typeof value === "string" ? value.split( " " ) : [ value ];

			for ( ; i < 4; i++ ) {
				expanded[ prefix + cssExpand[ i ] + suffix ] =
					parts[ i ] || parts[ i - 2 ] || parts[ 0 ];
			}

			return expanded;
		}
	};

	if ( !rmargin.test( prefix ) ) {
		jQuery.cssHooks[ prefix + suffix ].set = setPositiveNumber;
	}
} );

jQuery.fn.extend( {
	css: function( name, value ) {
		return access( this, function( elem, name, value ) {
			var styles, len,
				map = {},
				i = 0;

			if ( Array.isArray( name ) ) {
				styles = getStyles( elem );
				len = name.length;

				for ( ; i < len; i++ ) {
					map[ name[ i ] ] = jQuery.css( elem, name[ i ], false, styles );
				}

				return map;
			}

			return value !== undefined ?
				jQuery.style( elem, name, value ) :
				jQuery.css( elem, name );
		}, name, value, arguments.length > 1 );
	}
} );


function Tween( elem, options, prop, end, easing ) {
	return new Tween.prototype.init( elem, options, prop, end, easing );
}
jQuery.Tween = Tween;

Tween.prototype = {
	constructor: Tween,
	init: function( elem, options, prop, end, easing, unit ) {
		this.elem = elem;
		this.prop = prop;
		this.easing = easing || jQuery.easing._default;
		this.options = options;
		this.start = this.now = this.cur();
		this.end = end;
		this.unit = unit || ( jQuery.cssNumber[ prop ] ? "" : "px" );
	},
	cur: function() {
		var hooks = Tween.propHooks[ this.prop ];

		return hooks && hooks.get ?
			hooks.get( this ) :
			Tween.propHooks._default.get( this );
	},
	run: function( percent ) {
		var eased,
			hooks = Tween.propHooks[ this.prop ];

		if ( this.options.duration ) {
			this.pos = eased = jQuery.easing[ this.easing ](
				percent, this.options.duration * percent, 0, 1, this.options.duration
			);
		} else {
			this.pos = eased = percent;
		}
		this.now = ( this.end - this.start ) * eased + this.start;

		if ( this.options.step ) {
			this.options.step.call( this.elem, this.now, this );
		}

		if ( hooks && hooks.set ) {
			hooks.set( this );
		} else {
			Tween.propHooks._default.set( this );
		}
		return this;
	}
};

Tween.prototype.init.prototype = Tween.prototype;

Tween.propHooks = {
	_default: {
		get: function( tween ) {
			var result;

			// Use a property on the element directly when it is not a DOM element,
			// or when there is no matching style property that exists.
			if ( tween.elem.nodeType !== 1 ||
				tween.elem[ tween.prop ] != null && tween.elem.style[ tween.prop ] == null ) {
				return tween.elem[ tween.prop ];
			}

			// Passing an empty string as a 3rd parameter to .css will automatically
			// attempt a parseFloat and fallback to a string if the parse fails.
			// Simple values such as "10px" are parsed to Float;
			// complex values such as "rotate(1rad)" are returned as-is.
			result = jQuery.css( tween.elem, tween.prop, "" );

			// Empty strings, null, undefined and "auto" are converted to 0.
			return !result || result === "auto" ? 0 : result;
		},
		set: function( tween ) {

			// Use step hook for back compat.
			// Use cssHook if its there.
			// Use .style if available and use plain properties where available.
			if ( jQuery.fx.step[ tween.prop ] ) {
				jQuery.fx.step[ tween.prop ]( tween );
			} else if ( tween.elem.nodeType === 1 &&
				( tween.elem.style[ jQuery.cssProps[ tween.prop ] ] != null ||
					jQuery.cssHooks[ tween.prop ] ) ) {
				jQuery.style( tween.elem, tween.prop, tween.now + tween.unit );
			} else {
				tween.elem[ tween.prop ] = tween.now;
			}
		}
	}
};

// Support: IE <=9 only
// Panic based approach to setting things on disconnected nodes
Tween.propHooks.scrollTop = Tween.propHooks.scrollLeft = {
	set: function( tween ) {
		if ( tween.elem.nodeType && tween.elem.parentNode ) {
			tween.elem[ tween.prop ] = tween.now;
		}
	}
};

jQuery.easing = {
	linear: function( p ) {
		return p;
	},
	swing: function( p ) {
		return 0.5 - Math.cos( p * Math.PI ) / 2;
	},
	_default: "swing"
};

jQuery.fx = Tween.prototype.init;

// Back compat <1.8 extension point
jQuery.fx.step = {};




var
	fxNow, inProgress,
	rfxtypes = /^(?:toggle|show|hide)$/,
	rrun = /queueHooks$/;

function schedule() {
	if ( inProgress ) {
		if ( document.hidden === false && window.requestAnimationFrame ) {
			window.requestAnimationFrame( schedule );
		} else {
			window.setTimeout( schedule, jQuery.fx.interval );
		}

		jQuery.fx.tick();
	}
}

// Animations created synchronously will run synchronously
function createFxNow() {
	window.setTimeout( function() {
		fxNow = undefined;
	} );
	return ( fxNow = jQuery.now() );
}

// Generate parameters to create a standard animation
function genFx( type, includeWidth ) {
	var which,
		i = 0,
		attrs = { height: type };

	// If we include width, step value is 1 to do all cssExpand values,
	// otherwise step value is 2 to skip over Left and Right
	includeWidth = includeWidth ? 1 : 0;
	for ( ; i < 4; i += 2 - includeWidth ) {
		which = cssExpand[ i ];
		attrs[ "margin" + which ] = attrs[ "padding" + which ] = type;
	}

	if ( includeWidth ) {
		attrs.opacity = attrs.width = type;
	}

	return attrs;
}

function createTween( value, prop, animation ) {
	var tween,
		collection = ( Animation.tweeners[ prop ] || [] ).concat( Animation.tweeners[ "*" ] ),
		index = 0,
		length = collection.length;
	for ( ; index < length; index++ ) {
		if ( ( tween = collection[ index ].call( animation, prop, value ) ) ) {

			// We're done with this property
			return tween;
		}
	}
}

function defaultPrefilter( elem, props, opts ) {
	var prop, value, toggle, hooks, oldfire, propTween, restoreDisplay, display,
		isBox = "width" in props || "height" in props,
		anim = this,
		orig = {},
		style = elem.style,
		hidden = elem.nodeType && isHiddenWithinTree( elem ),
		dataShow = dataPriv.get( elem, "fxshow" );

	// Queue-skipping animations hijack the fx hooks
	if ( !opts.queue ) {
		hooks = jQuery._queueHooks( elem, "fx" );
		if ( hooks.unqueued == null ) {
			hooks.unqueued = 0;
			oldfire = hooks.empty.fire;
			hooks.empty.fire = function() {
				if ( !hooks.unqueued ) {
					oldfire();
				}
			};
		}
		hooks.unqueued++;

		anim.always( function() {

			// Ensure the complete handler is called before this completes
			anim.always( function() {
				hooks.unqueued--;
				if ( !jQuery.queue( elem, "fx" ).length ) {
					hooks.empty.fire();
				}
			} );
		} );
	}

	// Detect show/hide animations
	for ( prop in props ) {
		value = props[ prop ];
		if ( rfxtypes.test( value ) ) {
			delete props[ prop ];
			toggle = toggle || value === "toggle";
			if ( value === ( hidden ? "hide" : "show" ) ) {

				// Pretend to be hidden if this is a "show" and
				// there is still data from a stopped show/hide
				if ( value === "show" && dataShow && dataShow[ prop ] !== undefined ) {
					hidden = true;

				// Ignore all other no-op show/hide data
				} else {
					continue;
				}
			}
			orig[ prop ] = dataShow && dataShow[ prop ] || jQuery.style( elem, prop );
		}
	}

	// Bail out if this is a no-op like .hide().hide()
	propTween = !jQuery.isEmptyObject( props );
	if ( !propTween && jQuery.isEmptyObject( orig ) ) {
		return;
	}

	// Restrict "overflow" and "display" styles during box animations
	if ( isBox && elem.nodeType === 1 ) {

		// Support: IE <=9 - 11, Edge 12 - 13
		// Record all 3 overflow attributes because IE does not infer the shorthand
		// from identically-valued overflowX and overflowY
		opts.overflow = [ style.overflow, style.overflowX, style.overflowY ];

		// Identify a display type, preferring old show/hide data over the CSS cascade
		restoreDisplay = dataShow && dataShow.display;
		if ( restoreDisplay == null ) {
			restoreDisplay = dataPriv.get( elem, "display" );
		}
		display = jQuery.css( elem, "display" );
		if ( display === "none" ) {
			if ( restoreDisplay ) {
				display = restoreDisplay;
			} else {

				// Get nonempty value(s) by temporarily forcing visibility
				showHide( [ elem ], true );
				restoreDisplay = elem.style.display || restoreDisplay;
				display = jQuery.css( elem, "display" );
				showHide( [ elem ] );
			}
		}

		// Animate inline elements as inline-block
		if ( display === "inline" || display === "inline-block" && restoreDisplay != null ) {
			if ( jQuery.css( elem, "float" ) === "none" ) {

				// Restore the original display value at the end of pure show/hide animations
				if ( !propTween ) {
					anim.done( function() {
						style.display = restoreDisplay;
					} );
					if ( restoreDisplay == null ) {
						display = style.display;
						restoreDisplay = display === "none" ? "" : display;
					}
				}
				style.display = "inline-block";
			}
		}
	}

	if ( opts.overflow ) {
		style.overflow = "hidden";
		anim.always( function() {
			style.overflow = opts.overflow[ 0 ];
			style.overflowX = opts.overflow[ 1 ];
			style.overflowY = opts.overflow[ 2 ];
		} );
	}

	// Implement show/hide animations
	propTween = false;
	for ( prop in orig ) {

		// General show/hide setup for this element animation
		if ( !propTween ) {
			if ( dataShow ) {
				if ( "hidden" in dataShow ) {
					hidden = dataShow.hidden;
				}
			} else {
				dataShow = dataPriv.access( elem, "fxshow", { display: restoreDisplay } );
			}

			// Store hidden/visible for toggle so `.stop().toggle()` "reverses"
			if ( toggle ) {
				dataShow.hidden = !hidden;
			}

			// Show elements before animating them
			if ( hidden ) {
				showHide( [ elem ], true );
			}

			/* eslint-disable no-loop-func */

			anim.done( function() {

			/* eslint-enable no-loop-func */

				// The final step of a "hide" animation is actually hiding the element
				if ( !hidden ) {
					showHide( [ elem ] );
				}
				dataPriv.remove( elem, "fxshow" );
				for ( prop in orig ) {
					jQuery.style( elem, prop, orig[ prop ] );
				}
			} );
		}

		// Per-property setup
		propTween = createTween( hidden ? dataShow[ prop ] : 0, prop, anim );
		if ( !( prop in dataShow ) ) {
			dataShow[ prop ] = propTween.start;
			if ( hidden ) {
				propTween.end = propTween.start;
				propTween.start = 0;
			}
		}
	}
}

function propFilter( props, specialEasing ) {
	var index, name, easing, value, hooks;

	// camelCase, specialEasing and expand cssHook pass
	for ( index in props ) {
		name = jQuery.camelCase( index );
		easing = specialEasing[ name ];
		value = props[ index ];
		if ( Array.isArray( value ) ) {
			easing = value[ 1 ];
			value = props[ index ] = value[ 0 ];
		}

		if ( index !== name ) {
			props[ name ] = value;
			delete props[ index ];
		}

		hooks = jQuery.cssHooks[ name ];
		if ( hooks && "expand" in hooks ) {
			value = hooks.expand( value );
			delete props[ name ];

			// Not quite $.extend, this won't overwrite existing keys.
			// Reusing 'index' because we have the correct "name"
			for ( index in value ) {
				if ( !( index in props ) ) {
					props[ index ] = value[ index ];
					specialEasing[ index ] = easing;
				}
			}
		} else {
			specialEasing[ name ] = easing;
		}
	}
}

function Animation( elem, properties, options ) {
	var result,
		stopped,
		index = 0,
		length = Animation.prefilters.length,
		deferred = jQuery.Deferred().always( function() {

			// Don't match elem in the :animated selector
			delete tick.elem;
		} ),
		tick = function() {
			if ( stopped ) {
				return false;
			}
			var currentTime = fxNow || createFxNow(),
				remaining = Math.max( 0, animation.startTime + animation.duration - currentTime ),

				// Support: Android 2.3 only
				// Archaic crash bug won't allow us to use `1 - ( 0.5 || 0 )` (#12497)
				temp = remaining / animation.duration || 0,
				percent = 1 - temp,
				index = 0,
				length = animation.tweens.length;

			for ( ; index < length; index++ ) {
				animation.tweens[ index ].run( percent );
			}

			deferred.notifyWith( elem, [ animation, percent, remaining ] );

			// If there's more to do, yield
			if ( percent < 1 && length ) {
				return remaining;
			}

			// If this was an empty animation, synthesize a final progress notification
			if ( !length ) {
				deferred.notifyWith( elem, [ animation, 1, 0 ] );
			}

			// Resolve the animation and report its conclusion
			deferred.resolveWith( elem, [ animation ] );
			return false;
		},
		animation = deferred.promise( {
			elem: elem,
			props: jQuery.extend( {}, properties ),
			opts: jQuery.extend( true, {
				specialEasing: {},
				easing: jQuery.easing._default
			}, options ),
			originalProperties: properties,
			originalOptions: options,
			startTime: fxNow || createFxNow(),
			duration: options.duration,
			tweens: [],
			createTween: function( prop, end ) {
				var tween = jQuery.Tween( elem, animation.opts, prop, end,
						animation.opts.specialEasing[ prop ] || animation.opts.easing );
				animation.tweens.push( tween );
				return tween;
			},
			stop: function( gotoEnd ) {
				var index = 0,

					// If we are going to the end, we want to run all the tweens
					// otherwise we skip this part
					length = gotoEnd ? animation.tweens.length : 0;
				if ( stopped ) {
					return this;
				}
				stopped = true;
				for ( ; index < length; index++ ) {
					animation.tweens[ index ].run( 1 );
				}

				// Resolve when we played the last frame; otherwise, reject
				if ( gotoEnd ) {
					deferred.notifyWith( elem, [ animation, 1, 0 ] );
					deferred.resolveWith( elem, [ animation, gotoEnd ] );
				} else {
					deferred.rejectWith( elem, [ animation, gotoEnd ] );
				}
				return this;
			}
		} ),
		props = animation.props;

	propFilter( props, animation.opts.specialEasing );

	for ( ; index < length; index++ ) {
		result = Animation.prefilters[ index ].call( animation, elem, props, animation.opts );
		if ( result ) {
			if ( jQuery.isFunction( result.stop ) ) {
				jQuery._queueHooks( animation.elem, animation.opts.queue ).stop =
					jQuery.proxy( result.stop, result );
			}
			return result;
		}
	}

	jQuery.map( props, createTween, animation );

	if ( jQuery.isFunction( animation.opts.start ) ) {
		animation.opts.start.call( elem, animation );
	}

	// Attach callbacks from options
	animation
		.progress( animation.opts.progress )
		.done( animation.opts.done, animation.opts.complete )
		.fail( animation.opts.fail )
		.always( animation.opts.always );

	jQuery.fx.timer(
		jQuery.extend( tick, {
			elem: elem,
			anim: animation,
			queue: animation.opts.queue
		} )
	);

	return animation;
}

jQuery.Animation = jQuery.extend( Animation, {

	tweeners: {
		"*": [ function( prop, value ) {
			var tween = this.createTween( prop, value );
			adjustCSS( tween.elem, prop, rcssNum.exec( value ), tween );
			return tween;
		} ]
	},

	tweener: function( props, callback ) {
		if ( jQuery.isFunction( props ) ) {
			callback = props;
			props = [ "*" ];
		} else {
			props = props.match( rnothtmlwhite );
		}

		var prop,
			index = 0,
			length = props.length;

		for ( ; index < length; index++ ) {
			prop = props[ index ];
			Animation.tweeners[ prop ] = Animation.tweeners[ prop ] || [];
			Animation.tweeners[ prop ].unshift( callback );
		}
	},

	prefilters: [ defaultPrefilter ],

	prefilter: function( callback, prepend ) {
		if ( prepend ) {
			Animation.prefilters.unshift( callback );
		} else {
			Animation.prefilters.push( callback );
		}
	}
} );

jQuery.speed = function( speed, easing, fn ) {
	var opt = speed && typeof speed === "object" ? jQuery.extend( {}, speed ) : {
		complete: fn || !fn && easing ||
			jQuery.isFunction( speed ) && speed,
		duration: speed,
		easing: fn && easing || easing && !jQuery.isFunction( easing ) && easing
	};

	// Go to the end state if fx are off
	if ( jQuery.fx.off ) {
		opt.duration = 0;

	} else {
		if ( typeof opt.duration !== "number" ) {
			if ( opt.duration in jQuery.fx.speeds ) {
				opt.duration = jQuery.fx.speeds[ opt.duration ];

			} else {
				opt.duration = jQuery.fx.speeds._default;
			}
		}
	}

	// Normalize opt.queue - true/undefined/null -> "fx"
	if ( opt.queue == null || opt.queue === true ) {
		opt.queue = "fx";
	}

	// Queueing
	opt.old = opt.complete;

	opt.complete = function() {
		if ( jQuery.isFunction( opt.old ) ) {
			opt.old.call( this );
		}

		if ( opt.queue ) {
			jQuery.dequeue( this, opt.queue );
		}
	};

	return opt;
};

jQuery.fn.extend( {
	fadeTo: function( speed, to, easing, callback ) {

		// Show any hidden elements after setting opacity to 0
		return this.filter( isHiddenWithinTree ).css( "opacity", 0 ).show()

			// Animate to the value specified
			.end().animate( { opacity: to }, speed, easing, callback );
	},
	animate: function( prop, speed, easing, callback ) {
		var empty = jQuery.isEmptyObject( prop ),
			optall = jQuery.speed( speed, easing, callback ),
			doAnimation = function() {

				// Operate on a copy of prop so per-property easing won't be lost
				var anim = Animation( this, jQuery.extend( {}, prop ), optall );

				// Empty animations, or finishing resolves immediately
				if ( empty || dataPriv.get( this, "finish" ) ) {
					anim.stop( true );
				}
			};
			doAnimation.finish = doAnimation;

		return empty || optall.queue === false ?
			this.each( doAnimation ) :
			this.queue( optall.queue, doAnimation );
	},
	stop: function( type, clearQueue, gotoEnd ) {
		var stopQueue = function( hooks ) {
			var stop = hooks.stop;
			delete hooks.stop;
			stop( gotoEnd );
		};

		if ( typeof type !== "string" ) {
			gotoEnd = clearQueue;
			clearQueue = type;
			type = undefined;
		}
		if ( clearQueue && type !== false ) {
			this.queue( type || "fx", [] );
		}

		return this.each( function() {
			var dequeue = true,
				index = type != null && type + "queueHooks",
				timers = jQuery.timers,
				data = dataPriv.get( this );

			if ( index ) {
				if ( data[ index ] && data[ index ].stop ) {
					stopQueue( data[ index ] );
				}
			} else {
				for ( index in data ) {
					if ( data[ index ] && data[ index ].stop && rrun.test( index ) ) {
						stopQueue( data[ index ] );
					}
				}
			}

			for ( index = timers.length; index--; ) {
				if ( timers[ index ].elem === this &&
					( type == null || timers[ index ].queue === type ) ) {

					timers[ index ].anim.stop( gotoEnd );
					dequeue = false;
					timers.splice( index, 1 );
				}
			}

			// Start the next in the queue if the last step wasn't forced.
			// Timers currently will call their complete callbacks, which
			// will dequeue but only if they were gotoEnd.
			if ( dequeue || !gotoEnd ) {
				jQuery.dequeue( this, type );
			}
		} );
	},
	finish: function( type ) {
		if ( type !== false ) {
			type = type || "fx";
		}
		return this.each( function() {
			var index,
				data = dataPriv.get( this ),
				queue = data[ type + "queue" ],
				hooks = data[ type + "queueHooks" ],
				timers = jQuery.timers,
				length = queue ? queue.length : 0;

			// Enable finishing flag on private data
			data.finish = true;

			// Empty the queue first
			jQuery.queue( this, type, [] );

			if ( hooks && hooks.stop ) {
				hooks.stop.call( this, true );
			}

			// Look for any active animations, and finish them
			for ( index = timers.length; index--; ) {
				if ( timers[ index ].elem === this && timers[ index ].queue === type ) {
					timers[ index ].anim.stop( true );
					timers.splice( index, 1 );
				}
			}

			// Look for any animations in the old queue and finish them
			for ( index = 0; index < length; index++ ) {
				if ( queue[ index ] && queue[ index ].finish ) {
					queue[ index ].finish.call( this );
				}
			}

			// Turn off finishing flag
			delete data.finish;
		} );
	}
} );

jQuery.each( [ "toggle", "show", "hide" ], function( i, name ) {
	var cssFn = jQuery.fn[ name ];
	jQuery.fn[ name ] = function( speed, easing, callback ) {
		return speed == null || typeof speed === "boolean" ?
			cssFn.apply( this, arguments ) :
			this.animate( genFx( name, true ), speed, easing, callback );
	};
} );

// Generate shortcuts for custom animations
jQuery.each( {
	slideDown: genFx( "show" ),
	slideUp: genFx( "hide" ),
	slideToggle: genFx( "toggle" ),
	fadeIn: { opacity: "show" },
	fadeOut: { opacity: "hide" },
	fadeToggle: { opacity: "toggle" }
}, function( name, props ) {
	jQuery.fn[ name ] = function( speed, easing, callback ) {
		return this.animate( props, speed, easing, callback );
	};
} );

jQuery.timers = [];
jQuery.fx.tick = function() {
	var timer,
		i = 0,
		timers = jQuery.timers;

	fxNow = jQuery.now();

	for ( ; i < timers.length; i++ ) {
		timer = timers[ i ];

		// Run the timer and safely remove it when done (allowing for external removal)
		if ( !timer() && timers[ i ] === timer ) {
			timers.splice( i--, 1 );
		}
	}

	if ( !timers.length ) {
		jQuery.fx.stop();
	}
	fxNow = undefined;
};

jQuery.fx.timer = function( timer ) {
	jQuery.timers.push( timer );
	jQuery.fx.start();
};

jQuery.fx.interval = 13;
jQuery.fx.start = function() {
	if ( inProgress ) {
		return;
	}

	inProgress = true;
	schedule();
};

jQuery.fx.stop = function() {
	inProgress = null;
};

jQuery.fx.speeds = {
	slow: 600,
	fast: 200,

	// Default speed
	_default: 400
};


// Based off of the plugin by Clint Helfers, with permission.
// https://web.archive.org/web/20100324014747/http://blindsignals.com/index.php/2009/07/jquery-delay/
jQuery.fn.delay = function( time, type ) {
	time = jQuery.fx ? jQuery.fx.speeds[ time ] || time : time;
	type = type || "fx";

	return this.queue( type, function( next, hooks ) {
		var timeout = window.setTimeout( next, time );
		hooks.stop = function() {
			window.clearTimeout( timeout );
		};
	} );
};


( function() {
	var input = document.createElement( "input" ),
		select = document.createElement( "select" ),
		opt = select.appendChild( document.createElement( "option" ) );

	input.type = "checkbox";

	// Support: Android <=4.3 only
	// Default value for a checkbox should be "on"
	support.checkOn = input.value !== "";

	// Support: IE <=11 only
	// Must access selectedIndex to make default options select
	support.optSelected = opt.selected;

	// Support: IE <=11 only
	// An input loses its value after becoming a radio
	input = document.createElement( "input" );
	input.value = "t";
	input.type = "radio";
	support.radioValue = input.value === "t";
} )();


var boolHook,
	attrHandle = jQuery.expr.attrHandle;

jQuery.fn.extend( {
	attr: function( name, value ) {
		return access( this, jQuery.attr, name, value, arguments.length > 1 );
	},

	removeAttr: function( name ) {
		return this.each( function() {
			jQuery.removeAttr( this, name );
		} );
	}
} );

jQuery.extend( {
	attr: function( elem, name, value ) {
		var ret, hooks,
			nType = elem.nodeType;

		// Don't get/set attributes on text, comment and attribute nodes
		if ( nType === 3 || nType === 8 || nType === 2 ) {
			return;
		}

		// Fallback to prop when attributes are not supported
		if ( typeof elem.getAttribute === "undefined" ) {
			return jQuery.prop( elem, name, value );
		}

		// Attribute hooks are determined by the lowercase version
		// Grab necessary hook if one is defined
		if ( nType !== 1 || !jQuery.isXMLDoc( elem ) ) {
			hooks = jQuery.attrHooks[ name.toLowerCase() ] ||
				( jQuery.expr.match.bool.test( name ) ? boolHook : undefined );
		}

		if ( value !== undefined ) {
			if ( value === null ) {
				jQuery.removeAttr( elem, name );
				return;
			}

			if ( hooks && "set" in hooks &&
				( ret = hooks.set( elem, value, name ) ) !== undefined ) {
				return ret;
			}

			elem.setAttribute( name, value + "" );
			return value;
		}

		if ( hooks && "get" in hooks && ( ret = hooks.get( elem, name ) ) !== null ) {
			return ret;
		}

		ret = jQuery.find.attr( elem, name );

		// Non-existent attributes return null, we normalize to undefined
		return ret == null ? undefined : ret;
	},

	attrHooks: {
		type: {
			set: function( elem, value ) {
				if ( !support.radioValue && value === "radio" &&
					nodeName( elem, "input" ) ) {
					var val = elem.value;
					elem.setAttribute( "type", value );
					if ( val ) {
						elem.value = val;
					}
					return value;
				}
			}
		}
	},

	removeAttr: function( elem, value ) {
		var name,
			i = 0,

			// Attribute names can contain non-HTML whitespace characters
			// https://html.spec.whatwg.org/multipage/syntax.html#attributes-2
			attrNames = value && value.match( rnothtmlwhite );

		if ( attrNames && elem.nodeType === 1 ) {
			while ( ( name = attrNames[ i++ ] ) ) {
				elem.removeAttribute( name );
			}
		}
	}
} );

// Hooks for boolean attributes
boolHook = {
	set: function( elem, value, name ) {
		if ( value === false ) {

			// Remove boolean attributes when set to false
			jQuery.removeAttr( elem, name );
		} else {
			elem.setAttribute( name, name );
		}
		return name;
	}
};

jQuery.each( jQuery.expr.match.bool.source.match( /\w+/g ), function( i, name ) {
	var getter = attrHandle[ name ] || jQuery.find.attr;

	attrHandle[ name ] = function( elem, name, isXML ) {
		var ret, handle,
			lowercaseName = name.toLowerCase();

		if ( !isXML ) {

			// Avoid an infinite loop by temporarily removing this function from the getter
			handle = attrHandle[ lowercaseName ];
			attrHandle[ lowercaseName ] = ret;
			ret = getter( elem, name, isXML ) != null ?
				lowercaseName :
				null;
			attrHandle[ lowercaseName ] = handle;
		}
		return ret;
	};
} );




var rfocusable = /^(?:input|select|textarea|button)$/i,
	rclickable = /^(?:a|area)$/i;

jQuery.fn.extend( {
	prop: function( name, value ) {
		return access( this, jQuery.prop, name, value, arguments.length > 1 );
	},

	removeProp: function( name ) {
		return this.each( function() {
			delete this[ jQuery.propFix[ name ] || name ];
		} );
	}
} );

jQuery.extend( {
	prop: function( elem, name, value ) {
		var ret, hooks,
			nType = elem.nodeType;

		// Don't get/set properties on text, comment and attribute nodes
		if ( nType === 3 || nType === 8 || nType === 2 ) {
			return;
		}

		if ( nType !== 1 || !jQuery.isXMLDoc( elem ) ) {

			// Fix name and attach hooks
			name = jQuery.propFix[ name ] || name;
			hooks = jQuery.propHooks[ name ];
		}

		if ( value !== undefined ) {
			if ( hooks && "set" in hooks &&
				( ret = hooks.set( elem, value, name ) ) !== undefined ) {
				return ret;
			}

			return ( elem[ name ] = value );
		}

		if ( hooks && "get" in hooks && ( ret = hooks.get( elem, name ) ) !== null ) {
			return ret;
		}

		return elem[ name ];
	},

	propHooks: {
		tabIndex: {
			get: function( elem ) {

				// Support: IE <=9 - 11 only
				// elem.tabIndex doesn't always return the
				// correct value when it hasn't been explicitly set
				// https://web.archive.org/web/20141116233347/http://fluidproject.org/blog/2008/01/09/getting-setting-and-removing-tabindex-values-with-javascript/
				// Use proper attribute retrieval(#12072)
				var tabindex = jQuery.find.attr( elem, "tabindex" );

				if ( tabindex ) {
					return parseInt( tabindex, 10 );
				}

				if (
					rfocusable.test( elem.nodeName ) ||
					rclickable.test( elem.nodeName ) &&
					elem.href
				) {
					return 0;
				}

				return -1;
			}
		}
	},

	propFix: {
		"for": "htmlFor",
		"class": "className"
	}
} );

// Support: IE <=11 only
// Accessing the selectedIndex property
// forces the browser to respect setting selected
// on the option
// The getter ensures a default option is selected
// when in an optgroup
// eslint rule "no-unused-expressions" is disabled for this code
// since it considers such accessions noop
if ( !support.optSelected ) {
	jQuery.propHooks.selected = {
		get: function( elem ) {

			/* eslint no-unused-expressions: "off" */

			var parent = elem.parentNode;
			if ( parent && parent.parentNode ) {
				parent.parentNode.selectedIndex;
			}
			return null;
		},
		set: function( elem ) {

			/* eslint no-unused-expressions: "off" */

			var parent = elem.parentNode;
			if ( parent ) {
				parent.selectedIndex;

				if ( parent.parentNode ) {
					parent.parentNode.selectedIndex;
				}
			}
		}
	};
}

jQuery.each( [
	"tabIndex",
	"readOnly",
	"maxLength",
	"cellSpacing",
	"cellPadding",
	"rowSpan",
	"colSpan",
	"useMap",
	"frameBorder",
	"contentEditable"
], function() {
	jQuery.propFix[ this.toLowerCase() ] = this;
} );




	// Strip and collapse whitespace according to HTML spec
	// https://html.spec.whatwg.org/multipage/infrastructure.html#strip-and-collapse-whitespace
	function stripAndCollapse( value ) {
		var tokens = value.match( rnothtmlwhite ) || [];
		return tokens.join( " " );
	}


function getClass( elem ) {
	return elem.getAttribute && elem.getAttribute( "class" ) || "";
}

jQuery.fn.extend( {
	addClass: function( value ) {
		var classes, elem, cur, curValue, clazz, j, finalValue,
			i = 0;

		if ( jQuery.isFunction( value ) ) {
			return this.each( function( j ) {
				jQuery( this ).addClass( value.call( this, j, getClass( this ) ) );
			} );
		}

		if ( typeof value === "string" && value ) {
			classes = value.match( rnothtmlwhite ) || [];

			while ( ( elem = this[ i++ ] ) ) {
				curValue = getClass( elem );
				cur = elem.nodeType === 1 && ( " " + stripAndCollapse( curValue ) + " " );

				if ( cur ) {
					j = 0;
					while ( ( clazz = classes[ j++ ] ) ) {
						if ( cur.indexOf( " " + clazz + " " ) < 0 ) {
							cur += clazz + " ";
						}
					}

					// Only assign if different to avoid unneeded rendering.
					finalValue = stripAndCollapse( cur );
					if ( curValue !== finalValue ) {
						elem.setAttribute( "class", finalValue );
					}
				}
			}
		}

		return this;
	},

	removeClass: function( value ) {
		var classes, elem, cur, curValue, clazz, j, finalValue,
			i = 0;

		if ( jQuery.isFunction( value ) ) {
			return this.each( function( j ) {
				jQuery( this ).removeClass( value.call( this, j, getClass( this ) ) );
			} );
		}

		if ( !arguments.length ) {
			return this.attr( "class", "" );
		}

		if ( typeof value === "string" && value ) {
			classes = value.match( rnothtmlwhite ) || [];

			while ( ( elem = this[ i++ ] ) ) {
				curValue = getClass( elem );

				// This expression is here for better compressibility (see addClass)
				cur = elem.nodeType === 1 && ( " " + stripAndCollapse( curValue ) + " " );

				if ( cur ) {
					j = 0;
					while ( ( clazz = classes[ j++ ] ) ) {

						// Remove *all* instances
						while ( cur.indexOf( " " + clazz + " " ) > -1 ) {
							cur = cur.replace( " " + clazz + " ", " " );
						}
					}

					// Only assign if different to avoid unneeded rendering.
					finalValue = stripAndCollapse( cur );
					if ( curValue !== finalValue ) {
						elem.setAttribute( "class", finalValue );
					}
				}
			}
		}

		return this;
	},

	toggleClass: function( value, stateVal ) {
		var type = typeof value;

		if ( typeof stateVal === "boolean" && type === "string" ) {
			return stateVal ? this.addClass( value ) : this.removeClass( value );
		}

		if ( jQuery.isFunction( value ) ) {
			return this.each( function( i ) {
				jQuery( this ).toggleClass(
					value.call( this, i, getClass( this ), stateVal ),
					stateVal
				);
			} );
		}

		return this.each( function() {
			var className, i, self, classNames;

			if ( type === "string" ) {

				// Toggle individual class names
				i = 0;
				self = jQuery( this );
				classNames = value.match( rnothtmlwhite ) || [];

				while ( ( className = classNames[ i++ ] ) ) {

					// Check each className given, space separated list
					if ( self.hasClass( className ) ) {
						self.removeClass( className );
					} else {
						self.addClass( className );
					}
				}

			// Toggle whole class name
			} else if ( value === undefined || type === "boolean" ) {
				className = getClass( this );
				if ( className ) {

					// Store className if set
					dataPriv.set( this, "__className__", className );
				}

				// If the element has a class name or if we're passed `false`,
				// then remove the whole classname (if there was one, the above saved it).
				// Otherwise bring back whatever was previously saved (if anything),
				// falling back to the empty string if nothing was stored.
				if ( this.setAttribute ) {
					this.setAttribute( "class",
						className || value === false ?
						"" :
						dataPriv.get( this, "__className__" ) || ""
					);
				}
			}
		} );
	},

	hasClass: function( selector ) {
		var className, elem,
			i = 0;

		className = " " + selector + " ";
		while ( ( elem = this[ i++ ] ) ) {
			if ( elem.nodeType === 1 &&
				( " " + stripAndCollapse( getClass( elem ) ) + " " ).indexOf( className ) > -1 ) {
					return true;
			}
		}

		return false;
	}
} );




var rreturn = /\r/g;

jQuery.fn.extend( {
	val: function( value ) {
		var hooks, ret, isFunction,
			elem = this[ 0 ];

		if ( !arguments.length ) {
			if ( elem ) {
				hooks = jQuery.valHooks[ elem.type ] ||
					jQuery.valHooks[ elem.nodeName.toLowerCase() ];

				if ( hooks &&
					"get" in hooks &&
					( ret = hooks.get( elem, "value" ) ) !== undefined
				) {
					return ret;
				}

				ret = elem.value;

				// Handle most common string cases
				if ( typeof ret === "string" ) {
					return ret.replace( rreturn, "" );
				}

				// Handle cases where value is null/undef or number
				return ret == null ? "" : ret;
			}

			return;
		}

		isFunction = jQuery.isFunction( value );

		return this.each( function( i ) {
			var val;

			if ( this.nodeType !== 1 ) {
				return;
			}

			if ( isFunction ) {
				val = value.call( this, i, jQuery( this ).val() );
			} else {
				val = value;
			}

			// Treat null/undefined as ""; convert numbers to string
			if ( val == null ) {
				val = "";

			} else if ( typeof val === "number" ) {
				val += "";

			} else if ( Array.isArray( val ) ) {
				val = jQuery.map( val, function( value ) {
					return value == null ? "" : value + "";
				} );
			}

			hooks = jQuery.valHooks[ this.type ] || jQuery.valHooks[ this.nodeName.toLowerCase() ];

			// If set returns undefined, fall back to normal setting
			if ( !hooks || !( "set" in hooks ) || hooks.set( this, val, "value" ) === undefined ) {
				this.value = val;
			}
		} );
	}
} );

jQuery.extend( {
	valHooks: {
		option: {
			get: function( elem ) {

				var val = jQuery.find.attr( elem, "value" );
				return val != null ?
					val :

					// Support: IE <=10 - 11 only
					// option.text throws exceptions (#14686, #14858)
					// Strip and collapse whitespace
					// https://html.spec.whatwg.org/#strip-and-collapse-whitespace
					stripAndCollapse( jQuery.text( elem ) );
			}
		},
		select: {
			get: function( elem ) {
				var value, option, i,
					options = elem.options,
					index = elem.selectedIndex,
					one = elem.type === "select-one",
					values = one ? null : [],
					max = one ? index + 1 : options.length;

				if ( index < 0 ) {
					i = max;

				} else {
					i = one ? index : 0;
				}

				// Loop through all the selected options
				for ( ; i < max; i++ ) {
					option = options[ i ];

					// Support: IE <=9 only
					// IE8-9 doesn't update selected after form reset (#2551)
					if ( ( option.selected || i === index ) &&

							// Don't return options that are disabled or in a disabled optgroup
							!option.disabled &&
							( !option.parentNode.disabled ||
								!nodeName( option.parentNode, "optgroup" ) ) ) {

						// Get the specific value for the option
						value = jQuery( option ).val();

						// We don't need an array for one selects
						if ( one ) {
							return value;
						}

						// Multi-Selects return an array
						values.push( value );
					}
				}

				return values;
			},

			set: function( elem, value ) {
				var optionSet, option,
					options = elem.options,
					values = jQuery.makeArray( value ),
					i = options.length;

				while ( i-- ) {
					option = options[ i ];

					/* eslint-disable no-cond-assign */

					if ( option.selected =
						jQuery.inArray( jQuery.valHooks.option.get( option ), values ) > -1
					) {
						optionSet = true;
					}

					/* eslint-enable no-cond-assign */
				}

				// Force browsers to behave consistently when non-matching value is set
				if ( !optionSet ) {
					elem.selectedIndex = -1;
				}
				return values;
			}
		}
	}
} );

// Radios and checkboxes getter/setter
jQuery.each( [ "radio", "checkbox" ], function() {
	jQuery.valHooks[ this ] = {
		set: function( elem, value ) {
			if ( Array.isArray( value ) ) {
				return ( elem.checked = jQuery.inArray( jQuery( elem ).val(), value ) > -1 );
			}
		}
	};
	if ( !support.checkOn ) {
		jQuery.valHooks[ this ].get = function( elem ) {
			return elem.getAttribute( "value" ) === null ? "on" : elem.value;
		};
	}
} );




// Return jQuery for attributes-only inclusion


var rfocusMorph = /^(?:focusinfocus|focusoutblur)$/;

jQuery.extend( jQuery.event, {

	trigger: function( event, data, elem, onlyHandlers ) {

		var i, cur, tmp, bubbleType, ontype, handle, special,
			eventPath = [ elem || document ],
			type = hasOwn.call( event, "type" ) ? event.type : event,
			namespaces = hasOwn.call( event, "namespace" ) ? event.namespace.split( "." ) : [];

		cur = tmp = elem = elem || document;

		// Don't do events on text and comment nodes
		if ( elem.nodeType === 3 || elem.nodeType === 8 ) {
			return;
		}

		// focus/blur morphs to focusin/out; ensure we're not firing them right now
		if ( rfocusMorph.test( type + jQuery.event.triggered ) ) {
			return;
		}

		if ( type.indexOf( "." ) > -1 ) {

			// Namespaced trigger; create a regexp to match event type in handle()
			namespaces = type.split( "." );
			type = namespaces.shift();
			namespaces.sort();
		}
		ontype = type.indexOf( ":" ) < 0 && "on" + type;

		// Caller can pass in a jQuery.Event object, Object, or just an event type string
		event = event[ jQuery.expando ] ?
			event :
			new jQuery.Event( type, typeof event === "object" && event );

		// Trigger bitmask: & 1 for native handlers; & 2 for jQuery (always true)
		event.isTrigger = onlyHandlers ? 2 : 3;
		event.namespace = namespaces.join( "." );
		event.rnamespace = event.namespace ?
			new RegExp( "(^|\\.)" + namespaces.join( "\\.(?:.*\\.|)" ) + "(\\.|$)" ) :
			null;

		// Clean up the event in case it is being reused
		event.result = undefined;
		if ( !event.target ) {
			event.target = elem;
		}

		// Clone any incoming data and prepend the event, creating the handler arg list
		data = data == null ?
			[ event ] :
			jQuery.makeArray( data, [ event ] );

		// Allow special events to draw outside the lines
		special = jQuery.event.special[ type ] || {};
		if ( !onlyHandlers && special.trigger && special.trigger.apply( elem, data ) === false ) {
			return;
		}

		// Determine event propagation path in advance, per W3C events spec (#9951)
		// Bubble up to document, then to window; watch for a global ownerDocument var (#9724)
		if ( !onlyHandlers && !special.noBubble && !jQuery.isWindow( elem ) ) {

			bubbleType = special.delegateType || type;
			if ( !rfocusMorph.test( bubbleType + type ) ) {
				cur = cur.parentNode;
			}
			for ( ; cur; cur = cur.parentNode ) {
				eventPath.push( cur );
				tmp = cur;
			}

			// Only add window if we got to document (e.g., not plain obj or detached DOM)
			if ( tmp === ( elem.ownerDocument || document ) ) {
				eventPath.push( tmp.defaultView || tmp.parentWindow || window );
			}
		}

		// Fire handlers on the event path
		i = 0;
		while ( ( cur = eventPath[ i++ ] ) && !event.isPropagationStopped() ) {

			event.type = i > 1 ?
				bubbleType :
				special.bindType || type;

			// jQuery handler
			handle = ( dataPriv.get( cur, "events" ) || {} )[ event.type ] &&
				dataPriv.get( cur, "handle" );
			if ( handle ) {
				handle.apply( cur, data );
			}

			// Native handler
			handle = ontype && cur[ ontype ];
			if ( handle && handle.apply && acceptData( cur ) ) {
				event.result = handle.apply( cur, data );
				if ( event.result === false ) {
					event.preventDefault();
				}
			}
		}
		event.type = type;

		// If nobody prevented the default action, do it now
		if ( !onlyHandlers && !event.isDefaultPrevented() ) {

			if ( ( !special._default ||
				special._default.apply( eventPath.pop(), data ) === false ) &&
				acceptData( elem ) ) {

				// Call a native DOM method on the target with the same name as the event.
				// Don't do default actions on window, that's where global variables be (#6170)
				if ( ontype && jQuery.isFunction( elem[ type ] ) && !jQuery.isWindow( elem ) ) {

					// Don't re-trigger an onFOO event when we call its FOO() method
					tmp = elem[ ontype ];

					if ( tmp ) {
						elem[ ontype ] = null;
					}

					// Prevent re-triggering of the same event, since we already bubbled it above
					jQuery.event.triggered = type;
					elem[ type ]();
					jQuery.event.triggered = undefined;

					if ( tmp ) {
						elem[ ontype ] = tmp;
					}
				}
			}
		}

		return event.result;
	},

	// Piggyback on a donor event to simulate a different one
	// Used only for `focus(in | out)` events
	simulate: function( type, elem, event ) {
		var e = jQuery.extend(
			new jQuery.Event(),
			event,
			{
				type: type,
				isSimulated: true
			}
		);

		jQuery.event.trigger( e, null, elem );
	}

} );

jQuery.fn.extend( {

	trigger: function( type, data ) {
		return this.each( function() {
			jQuery.event.trigger( type, data, this );
		} );
	},
	triggerHandler: function( type, data ) {
		var elem = this[ 0 ];
		if ( elem ) {
			return jQuery.event.trigger( type, data, elem, true );
		}
	}
} );


jQuery.each( ( "blur focus focusin focusout resize scroll click dblclick " +
	"mousedown mouseup mousemove mouseover mouseout mouseenter mouseleave " +
	"change select submit keydown keypress keyup contextmenu" ).split( " " ),
	function( i, name ) {

	// Handle event binding
	jQuery.fn[ name ] = function( data, fn ) {
		return arguments.length > 0 ?
			this.on( name, null, data, fn ) :
			this.trigger( name );
	};
} );

jQuery.fn.extend( {
	hover: function( fnOver, fnOut ) {
		return this.mouseenter( fnOver ).mouseleave( fnOut || fnOver );
	}
} );




support.focusin = "onfocusin" in window;


// Support: Firefox <=44
// Firefox doesn't have focus(in | out) events
// Related ticket - https://bugzilla.mozilla.org/show_bug.cgi?id=687787
//
// Support: Chrome <=48 - 49, Safari <=9.0 - 9.1
// focus(in | out) events fire after focus & blur events,
// which is spec violation - http://www.w3.org/TR/DOM-Level-3-Events/#events-focusevent-event-order
// Related ticket - https://bugs.chromium.org/p/chromium/issues/detail?id=449857
if ( !support.focusin ) {
	jQuery.each( { focus: "focusin", blur: "focusout" }, function( orig, fix ) {

		// Attach a single capturing handler on the document while someone wants focusin/focusout
		var handler = function( event ) {
			jQuery.event.simulate( fix, event.target, jQuery.event.fix( event ) );
		};

		jQuery.event.special[ fix ] = {
			setup: function() {
				var doc = this.ownerDocument || this,
					attaches = dataPriv.access( doc, fix );

				if ( !attaches ) {
					doc.addEventListener( orig, handler, true );
				}
				dataPriv.access( doc, fix, ( attaches || 0 ) + 1 );
			},
			teardown: function() {
				var doc = this.ownerDocument || this,
					attaches = dataPriv.access( doc, fix ) - 1;

				if ( !attaches ) {
					doc.removeEventListener( orig, handler, true );
					dataPriv.remove( doc, fix );

				} else {
					dataPriv.access( doc, fix, attaches );
				}
			}
		};
	} );
}
var location = window.location;

var nonce = jQuery.now();

var rquery = ( /\?/ );



// Cross-browser xml parsing
jQuery.parseXML = function( data ) {
	var xml;
	if ( !data || typeof data !== "string" ) {
		return null;
	}

	// Support: IE 9 - 11 only
	// IE throws on parseFromString with invalid input.
	try {
		xml = ( new window.DOMParser() ).parseFromString( data, "text/xml" );
	} catch ( e ) {
		xml = undefined;
	}

	if ( !xml || xml.getElementsByTagName( "parsererror" ).length ) {
		jQuery.error( "Invalid XML: " + data );
	}
	return xml;
};


var
	rbracket = /\[\]$/,
	rCRLF = /\r?\n/g,
	rsubmitterTypes = /^(?:submit|button|image|reset|file)$/i,
	rsubmittable = /^(?:input|select|textarea|keygen)/i;

function buildParams( prefix, obj, traditional, add ) {
	var name;

	if ( Array.isArray( obj ) ) {

		// Serialize array item.
		jQuery.each( obj, function( i, v ) {
			if ( traditional || rbracket.test( prefix ) ) {

				// Treat each array item as a scalar.
				add( prefix, v );

			} else {

				// Item is non-scalar (array or object), encode its numeric index.
				buildParams(
					prefix + "[" + ( typeof v === "object" && v != null ? i : "" ) + "]",
					v,
					traditional,
					add
				);
			}
		} );

	} else if ( !traditional && jQuery.type( obj ) === "object" ) {

		// Serialize object item.
		for ( name in obj ) {
			buildParams( prefix + "[" + name + "]", obj[ name ], traditional, add );
		}

	} else {

		// Serialize scalar item.
		add( prefix, obj );
	}
}

// Serialize an array of form elements or a set of
// key/values into a query string
jQuery.param = function( a, traditional ) {
	var prefix,
		s = [],
		add = function( key, valueOrFunction ) {

			// If value is a function, invoke it and use its return value
			var value = jQuery.isFunction( valueOrFunction ) ?
				valueOrFunction() :
				valueOrFunction;

			s[ s.length ] = encodeURIComponent( key ) + "=" +
				encodeURIComponent( value == null ? "" : value );
		};

	// If an array was passed in, assume that it is an array of form elements.
	if ( Array.isArray( a ) || ( a.jquery && !jQuery.isPlainObject( a ) ) ) {

		// Serialize the form elements
		jQuery.each( a, function() {
			add( this.name, this.value );
		} );

	} else {

		// If traditional, encode the "old" way (the way 1.3.2 or older
		// did it), otherwise encode params recursively.
		for ( prefix in a ) {
			buildParams( prefix, a[ prefix ], traditional, add );
		}
	}

	// Return the resulting serialization
	return s.join( "&" );
};

jQuery.fn.extend( {
	serialize: function() {
		return jQuery.param( this.serializeArray() );
	},
	serializeArray: function() {
		return this.map( function() {

			// Can add propHook for "elements" to filter or add form elements
			var elements = jQuery.prop( this, "elements" );
			return elements ? jQuery.makeArray( elements ) : this;
		} )
		.filter( function() {
			var type = this.type;

			// Use .is( ":disabled" ) so that fieldset[disabled] works
			return this.name && !jQuery( this ).is( ":disabled" ) &&
				rsubmittable.test( this.nodeName ) && !rsubmitterTypes.test( type ) &&
				( this.checked || !rcheckableType.test( type ) );
		} )
		.map( function( i, elem ) {
			var val = jQuery( this ).val();

			if ( val == null ) {
				return null;
			}

			if ( Array.isArray( val ) ) {
				return jQuery.map( val, function( val ) {
					return { name: elem.name, value: val.replace( rCRLF, "\r\n" ) };
				} );
			}

			return { name: elem.name, value: val.replace( rCRLF, "\r\n" ) };
		} ).get();
	}
} );


var
	r20 = /%20/g,
	rhash = /#.*$/,
	rantiCache = /([?&])_=[^&]*/,
	rheaders = /^(.*?):[ \t]*([^\r\n]*)$/mg,

	// #7653, #8125, #8152: local protocol detection
	rlocalProtocol = /^(?:about|app|app-storage|.+-extension|file|res|widget):$/,
	rnoContent = /^(?:GET|HEAD)$/,
	rprotocol = /^\/\//,

	/* Prefilters
	 * 1) They are useful to introduce custom dataTypes (see ajax/jsonp.js for an example)
	 * 2) These are called:
	 *    - BEFORE asking for a transport
	 *    - AFTER param serialization (s.data is a string if s.processData is true)
	 * 3) key is the dataType
	 * 4) the catchall symbol "*" can be used
	 * 5) execution will start with transport dataType and THEN continue down to "*" if needed
	 */
	prefilters = {},

	/* Transports bindings
	 * 1) key is the dataType
	 * 2) the catchall symbol "*" can be used
	 * 3) selection will start with transport dataType and THEN go to "*" if needed
	 */
	transports = {},

	// Avoid comment-prolog char sequence (#10098); must appease lint and evade compression
	allTypes = "*/".concat( "*" ),

	// Anchor tag for parsing the document origin
	originAnchor = document.createElement( "a" );
	originAnchor.href = location.href;

// Base "constructor" for jQuery.ajaxPrefilter and jQuery.ajaxTransport
function addToPrefiltersOrTransports( structure ) {

	// dataTypeExpression is optional and defaults to "*"
	return function( dataTypeExpression, func ) {

		if ( typeof dataTypeExpression !== "string" ) {
			func = dataTypeExpression;
			dataTypeExpression = "*";
		}

		var dataType,
			i = 0,
			dataTypes = dataTypeExpression.toLowerCase().match( rnothtmlwhite ) || [];

		if ( jQuery.isFunction( func ) ) {

			// For each dataType in the dataTypeExpression
			while ( ( dataType = dataTypes[ i++ ] ) ) {

				// Prepend if requested
				if ( dataType[ 0 ] === "+" ) {
					dataType = dataType.slice( 1 ) || "*";
					( structure[ dataType ] = structure[ dataType ] || [] ).unshift( func );

				// Otherwise append
				} else {
					( structure[ dataType ] = structure[ dataType ] || [] ).push( func );
				}
			}
		}
	};
}

// Base inspection function for prefilters and transports
function inspectPrefiltersOrTransports( structure, options, originalOptions, jqXHR ) {

	var inspected = {},
		seekingTransport = ( structure === transports );

	function inspect( dataType ) {
		var selected;
		inspected[ dataType ] = true;
		jQuery.each( structure[ dataType ] || [], function( _, prefilterOrFactory ) {
			var dataTypeOrTransport = prefilterOrFactory( options, originalOptions, jqXHR );
			if ( typeof dataTypeOrTransport === "string" &&
				!seekingTransport && !inspected[ dataTypeOrTransport ] ) {

				options.dataTypes.unshift( dataTypeOrTransport );
				inspect( dataTypeOrTransport );
				return false;
			} else if ( seekingTransport ) {
				return !( selected = dataTypeOrTransport );
			}
		} );
		return selected;
	}

	return inspect( options.dataTypes[ 0 ] ) || !inspected[ "*" ] && inspect( "*" );
}

// A special extend for ajax options
// that takes "flat" options (not to be deep extended)
// Fixes #9887
function ajaxExtend( target, src ) {
	var key, deep,
		flatOptions = jQuery.ajaxSettings.flatOptions || {};

	for ( key in src ) {
		if ( src[ key ] !== undefined ) {
			( flatOptions[ key ] ? target : ( deep || ( deep = {} ) ) )[ key ] = src[ key ];
		}
	}
	if ( deep ) {
		jQuery.extend( true, target, deep );
	}

	return target;
}

/* Handles responses to an ajax request:
 * - finds the right dataType (mediates between content-type and expected dataType)
 * - returns the corresponding response
 */
function ajaxHandleResponses( s, jqXHR, responses ) {

	var ct, type, finalDataType, firstDataType,
		contents = s.contents,
		dataTypes = s.dataTypes;

	// Remove auto dataType and get content-type in the process
	while ( dataTypes[ 0 ] === "*" ) {
		dataTypes.shift();
		if ( ct === undefined ) {
			ct = s.mimeType || jqXHR.getResponseHeader( "Content-Type" );
		}
	}

	// Check if we're dealing with a known content-type
	if ( ct ) {
		for ( type in contents ) {
			if ( contents[ type ] && contents[ type ].test( ct ) ) {
				dataTypes.unshift( type );
				break;
			}
		}
	}

	// Check to see if we have a response for the expected dataType
	if ( dataTypes[ 0 ] in responses ) {
		finalDataType = dataTypes[ 0 ];
	} else {

		// Try convertible dataTypes
		for ( type in responses ) {
			if ( !dataTypes[ 0 ] || s.converters[ type + " " + dataTypes[ 0 ] ] ) {
				finalDataType = type;
				break;
			}
			if ( !firstDataType ) {
				firstDataType = type;
			}
		}

		// Or just use first one
		finalDataType = finalDataType || firstDataType;
	}

	// If we found a dataType
	// We add the dataType to the list if needed
	// and return the corresponding response
	if ( finalDataType ) {
		if ( finalDataType !== dataTypes[ 0 ] ) {
			dataTypes.unshift( finalDataType );
		}
		return responses[ finalDataType ];
	}
}

/* Chain conversions given the request and the original response
 * Also sets the responseXXX fields on the jqXHR instance
 */
function ajaxConvert( s, response, jqXHR, isSuccess ) {
	var conv2, current, conv, tmp, prev,
		converters = {},

		// Work with a copy of dataTypes in case we need to modify it for conversion
		dataTypes = s.dataTypes.slice();

	// Create converters map with lowercased keys
	if ( dataTypes[ 1 ] ) {
		for ( conv in s.converters ) {
			converters[ conv.toLowerCase() ] = s.converters[ conv ];
		}
	}

	current = dataTypes.shift();

	// Convert to each sequential dataType
	while ( current ) {

		if ( s.responseFields[ current ] ) {
			jqXHR[ s.responseFields[ current ] ] = response;
		}

		// Apply the dataFilter if provided
		if ( !prev && isSuccess && s.dataFilter ) {
			response = s.dataFilter( response, s.dataType );
		}

		prev = current;
		current = dataTypes.shift();

		if ( current ) {

			// There's only work to do if current dataType is non-auto
			if ( current === "*" ) {

				current = prev;

			// Convert response if prev dataType is non-auto and differs from current
			} else if ( prev !== "*" && prev !== current ) {

				// Seek a direct converter
				conv = converters[ prev + " " + current ] || converters[ "* " + current ];

				// If none found, seek a pair
				if ( !conv ) {
					for ( conv2 in converters ) {

						// If conv2 outputs current
						tmp = conv2.split( " " );
						if ( tmp[ 1 ] === current ) {

							// If prev can be converted to accepted input
							conv = converters[ prev + " " + tmp[ 0 ] ] ||
								converters[ "* " + tmp[ 0 ] ];
							if ( conv ) {

								// Condense equivalence converters
								if ( conv === true ) {
									conv = converters[ conv2 ];

								// Otherwise, insert the intermediate dataType
								} else if ( converters[ conv2 ] !== true ) {
									current = tmp[ 0 ];
									dataTypes.unshift( tmp[ 1 ] );
								}
								break;
							}
						}
					}
				}

				// Apply converter (if not an equivalence)
				if ( conv !== true ) {

					// Unless errors are allowed to bubble, catch and return them
					if ( conv && s.throws ) {
						response = conv( response );
					} else {
						try {
							response = conv( response );
						} catch ( e ) {
							return {
								state: "parsererror",
								error: conv ? e : "No conversion from " + prev + " to " + current
							};
						}
					}
				}
			}
		}
	}

	return { state: "success", data: response };
}

jQuery.extend( {

	// Counter for holding the number of active queries
	active: 0,

	// Last-Modified header cache for next request
	lastModified: {},
	etag: {},

	ajaxSettings: {
		url: location.href,
		type: "GET",
		isLocal: rlocalProtocol.test( location.protocol ),
		global: true,
		processData: true,
		async: true,
		contentType: "application/x-www-form-urlencoded; charset=UTF-8",

		/*
		timeout: 0,
		data: null,
		dataType: null,
		username: null,
		password: null,
		cache: null,
		throws: false,
		traditional: false,
		headers: {},
		*/

		accepts: {
			"*": allTypes,
			text: "text/plain",
			html: "text/html",
			xml: "application/xml, text/xml",
			json: "application/json, text/javascript"
		},

		contents: {
			xml: /\bxml\b/,
			html: /\bhtml/,
			json: /\bjson\b/
		},

		responseFields: {
			xml: "responseXML",
			text: "responseText",
			json: "responseJSON"
		},

		// Data converters
		// Keys separate source (or catchall "*") and destination types with a single space
		converters: {

			// Convert anything to text
			"* text": String,

			// Text to html (true = no transformation)
			"text html": true,

			// Evaluate text as a json expression
			"text json": JSON.parse,

			// Parse text as xml
			"text xml": jQuery.parseXML
		},

		// For options that shouldn't be deep extended:
		// you can add your own custom options here if
		// and when you create one that shouldn't be
		// deep extended (see ajaxExtend)
		flatOptions: {
			url: true,
			context: true
		}
	},

	// Creates a full fledged settings object into target
	// with both ajaxSettings and settings fields.
	// If target is omitted, writes into ajaxSettings.
	ajaxSetup: function( target, settings ) {
		return settings ?

			// Building a settings object
			ajaxExtend( ajaxExtend( target, jQuery.ajaxSettings ), settings ) :

			// Extending ajaxSettings
			ajaxExtend( jQuery.ajaxSettings, target );
	},

	ajaxPrefilter: addToPrefiltersOrTransports( prefilters ),
	ajaxTransport: addToPrefiltersOrTransports( transports ),

	// Main method
	ajax: function( url, options ) {

		// If url is an object, simulate pre-1.5 signature
		if ( typeof url === "object" ) {
			options = url;
			url = undefined;
		}

		// Force options to be an object
		options = options || {};

		var transport,

			// URL without anti-cache param
			cacheURL,

			// Response headers
			responseHeadersString,
			responseHeaders,

			// timeout handle
			timeoutTimer,

			// Url cleanup var
			urlAnchor,

			// Request state (becomes false upon send and true upon completion)
			completed,

			// To know if global events are to be dispatched
			fireGlobals,

			// Loop variable
			i,

			// uncached part of the url
			uncached,

			// Create the final options object
			s = jQuery.ajaxSetup( {}, options ),

			// Callbacks context
			callbackContext = s.context || s,

			// Context for global events is callbackContext if it is a DOM node or jQuery collection
			globalEventContext = s.context &&
				( callbackContext.nodeType || callbackContext.jquery ) ?
					jQuery( callbackContext ) :
					jQuery.event,

			// Deferreds
			deferred = jQuery.Deferred(),
			completeDeferred = jQuery.Callbacks( "once memory" ),

			// Status-dependent callbacks
			statusCode = s.statusCode || {},

			// Headers (they are sent all at once)
			requestHeaders = {},
			requestHeadersNames = {},

			// Default abort message
			strAbort = "canceled",

			// Fake xhr
			jqXHR = {
				readyState: 0,

				// Builds headers hashtable if needed
				getResponseHeader: function( key ) {
					var match;
					if ( completed ) {
						if ( !responseHeaders ) {
							responseHeaders = {};
							while ( ( match = rheaders.exec( responseHeadersString ) ) ) {
								responseHeaders[ match[ 1 ].toLowerCase() ] = match[ 2 ];
							}
						}
						match = responseHeaders[ key.toLowerCase() ];
					}
					return match == null ? null : match;
				},

				// Raw string
				getAllResponseHeaders: function() {
					return completed ? responseHeadersString : null;
				},

				// Caches the header
				setRequestHeader: function( name, value ) {
					if ( completed == null ) {
						name = requestHeadersNames[ name.toLowerCase() ] =
							requestHeadersNames[ name.toLowerCase() ] || name;
						requestHeaders[ name ] = value;
					}
					return this;
				},

				// Overrides response content-type header
				overrideMimeType: function( type ) {
					if ( completed == null ) {
						s.mimeType = type;
					}
					return this;
				},

				// Status-dependent callbacks
				statusCode: function( map ) {
					var code;
					if ( map ) {
						if ( completed ) {

							// Execute the appropriate callbacks
							jqXHR.always( map[ jqXHR.status ] );
						} else {

							// Lazy-add the new callbacks in a way that preserves old ones
							for ( code in map ) {
								statusCode[ code ] = [ statusCode[ code ], map[ code ] ];
							}
						}
					}
					return this;
				},

				// Cancel the request
				abort: function( statusText ) {
					var finalText = statusText || strAbort;
					if ( transport ) {
						transport.abort( finalText );
					}
					done( 0, finalText );
					return this;
				}
			};

		// Attach deferreds
		deferred.promise( jqXHR );

		// Add protocol if not provided (prefilters might expect it)
		// Handle falsy url in the settings object (#10093: consistency with old signature)
		// We also use the url parameter if available
		s.url = ( ( url || s.url || location.href ) + "" )
			.replace( rprotocol, location.protocol + "//" );

		// Alias method option to type as per ticket #12004
		s.type = options.method || options.type || s.method || s.type;

		// Extract dataTypes list
		s.dataTypes = ( s.dataType || "*" ).toLowerCase().match( rnothtmlwhite ) || [ "" ];

		// A cross-domain request is in order when the origin doesn't match the current origin.
		if ( s.crossDomain == null ) {
			urlAnchor = document.createElement( "a" );

			// Support: IE <=8 - 11, Edge 12 - 13
			// IE throws exception on accessing the href property if url is malformed,
			// e.g. http://example.com:80x/
			try {
				urlAnchor.href = s.url;

				// Support: IE <=8 - 11 only
				// Anchor's host property isn't correctly set when s.url is relative
				urlAnchor.href = urlAnchor.href;
				s.crossDomain = originAnchor.protocol + "//" + originAnchor.host !==
					urlAnchor.protocol + "//" + urlAnchor.host;
			} catch ( e ) {

				// If there is an error parsing the URL, assume it is crossDomain,
				// it can be rejected by the transport if it is invalid
				s.crossDomain = true;
			}
		}

		// Convert data if not already a string
		if ( s.data && s.processData && typeof s.data !== "string" ) {
			s.data = jQuery.param( s.data, s.traditional );
		}

		// Apply prefilters
		inspectPrefiltersOrTransports( prefilters, s, options, jqXHR );

		// If request was aborted inside a prefilter, stop there
		if ( completed ) {
			return jqXHR;
		}

		// We can fire global events as of now if asked to
		// Don't fire events if jQuery.event is undefined in an AMD-usage scenario (#15118)
		fireGlobals = jQuery.event && s.global;

		// Watch for a new set of requests
		if ( fireGlobals && jQuery.active++ === 0 ) {
			jQuery.event.trigger( "ajaxStart" );
		}

		// Uppercase the type
		s.type = s.type.toUpperCase();

		// Determine if request has content
		s.hasContent = !rnoContent.test( s.type );

		// Save the URL in case we're toying with the If-Modified-Since
		// and/or If-None-Match header later on
		// Remove hash to simplify url manipulation
		cacheURL = s.url.replace( rhash, "" );

		// More options handling for requests with no content
		if ( !s.hasContent ) {

			// Remember the hash so we can put it back
			uncached = s.url.slice( cacheURL.length );

			// If data is available, append data to url
			if ( s.data ) {
				cacheURL += ( rquery.test( cacheURL ) ? "&" : "?" ) + s.data;

				// #9682: remove data so that it's not used in an eventual retry
				delete s.data;
			}

			// Add or update anti-cache param if needed
			if ( s.cache === false ) {
				cacheURL = cacheURL.replace( rantiCache, "$1" );
				uncached = ( rquery.test( cacheURL ) ? "&" : "?" ) + "_=" + ( nonce++ ) + uncached;
			}

			// Put hash and anti-cache on the URL that will be requested (gh-1732)
			s.url = cacheURL + uncached;

		// Change '%20' to '+' if this is encoded form body content (gh-2658)
		} else if ( s.data && s.processData &&
			( s.contentType || "" ).indexOf( "application/x-www-form-urlencoded" ) === 0 ) {
			s.data = s.data.replace( r20, "+" );
		}

		// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
		if ( s.ifModified ) {
			if ( jQuery.lastModified[ cacheURL ] ) {
				jqXHR.setRequestHeader( "If-Modified-Since", jQuery.lastModified[ cacheURL ] );
			}
			if ( jQuery.etag[ cacheURL ] ) {
				jqXHR.setRequestHeader( "If-None-Match", jQuery.etag[ cacheURL ] );
			}
		}

		// Set the correct header, if data is being sent
		if ( s.data && s.hasContent && s.contentType !== false || options.contentType ) {
			jqXHR.setRequestHeader( "Content-Type", s.contentType );
		}

		// Set the Accepts header for the server, depending on the dataType
		jqXHR.setRequestHeader(
			"Accept",
			s.dataTypes[ 0 ] && s.accepts[ s.dataTypes[ 0 ] ] ?
				s.accepts[ s.dataTypes[ 0 ] ] +
					( s.dataTypes[ 0 ] !== "*" ? ", " + allTypes + "; q=0.01" : "" ) :
				s.accepts[ "*" ]
		);

		// Check for headers option
		for ( i in s.headers ) {
			jqXHR.setRequestHeader( i, s.headers[ i ] );
		}

		// Allow custom headers/mimetypes and early abort
		if ( s.beforeSend &&
			( s.beforeSend.call( callbackContext, jqXHR, s ) === false || completed ) ) {

			// Abort if not done already and return
			return jqXHR.abort();
		}

		// Aborting is no longer a cancellation
		strAbort = "abort";

		// Install callbacks on deferreds
		completeDeferred.add( s.complete );
		jqXHR.done( s.success );
		jqXHR.fail( s.error );

		// Get transport
		transport = inspectPrefiltersOrTransports( transports, s, options, jqXHR );

		// If no transport, we auto-abort
		if ( !transport ) {
			done( -1, "No Transport" );
		} else {
			jqXHR.readyState = 1;

			// Send global event
			if ( fireGlobals ) {
				globalEventContext.trigger( "ajaxSend", [ jqXHR, s ] );
			}

			// If request was aborted inside ajaxSend, stop there
			if ( completed ) {
				return jqXHR;
			}

			// Timeout
			if ( s.async && s.timeout > 0 ) {
				timeoutTimer = window.setTimeout( function() {
					jqXHR.abort( "timeout" );
				}, s.timeout );
			}

			try {
				completed = false;
				transport.send( requestHeaders, done );
			} catch ( e ) {

				// Rethrow post-completion exceptions
				if ( completed ) {
					throw e;
				}

				// Propagate others as results
				done( -1, e );
			}
		}

		// Callback for when everything is done
		function done( status, nativeStatusText, responses, headers ) {
			var isSuccess, success, error, response, modified,
				statusText = nativeStatusText;

			// Ignore repeat invocations
			if ( completed ) {
				return;
			}

			completed = true;

			// Clear timeout if it exists
			if ( timeoutTimer ) {
				window.clearTimeout( timeoutTimer );
			}

			// Dereference transport for early garbage collection
			// (no matter how long the jqXHR object will be used)
			transport = undefined;

			// Cache response headers
			responseHeadersString = headers || "";

			// Set readyState
			jqXHR.readyState = status > 0 ? 4 : 0;

			// Determine if successful
			isSuccess = status >= 200 && status < 300 || status === 304;

			// Get response data
			if ( responses ) {
				response = ajaxHandleResponses( s, jqXHR, responses );
			}

			// Convert no matter what (that way responseXXX fields are always set)
			response = ajaxConvert( s, response, jqXHR, isSuccess );

			// If successful, handle type chaining
			if ( isSuccess ) {

				// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
				if ( s.ifModified ) {
					modified = jqXHR.getResponseHeader( "Last-Modified" );
					if ( modified ) {
						jQuery.lastModified[ cacheURL ] = modified;
					}
					modified = jqXHR.getResponseHeader( "etag" );
					if ( modified ) {
						jQuery.etag[ cacheURL ] = modified;
					}
				}

				// if no content
				if ( status === 204 || s.type === "HEAD" ) {
					statusText = "nocontent";

				// if not modified
				} else if ( status === 304 ) {
					statusText = "notmodified";

				// If we have data, let's convert it
				} else {
					statusText = response.state;
					success = response.data;
					error = response.error;
					isSuccess = !error;
				}
			} else {

				// Extract error from statusText and normalize for non-aborts
				error = statusText;
				if ( status || !statusText ) {
					statusText = "error";
					if ( status < 0 ) {
						status = 0;
					}
				}
			}

			// Set data for the fake xhr object
			jqXHR.status = status;
			jqXHR.statusText = ( nativeStatusText || statusText ) + "";

			// Success/Error
			if ( isSuccess ) {
				deferred.resolveWith( callbackContext, [ success, statusText, jqXHR ] );
			} else {
				deferred.rejectWith( callbackContext, [ jqXHR, statusText, error ] );
			}

			// Status-dependent callbacks
			jqXHR.statusCode( statusCode );
			statusCode = undefined;

			if ( fireGlobals ) {
				globalEventContext.trigger( isSuccess ? "ajaxSuccess" : "ajaxError",
					[ jqXHR, s, isSuccess ? success : error ] );
			}

			// Complete
			completeDeferred.fireWith( callbackContext, [ jqXHR, statusText ] );

			if ( fireGlobals ) {
				globalEventContext.trigger( "ajaxComplete", [ jqXHR, s ] );

				// Handle the global AJAX counter
				if ( !( --jQuery.active ) ) {
					jQuery.event.trigger( "ajaxStop" );
				}
			}
		}

		return jqXHR;
	},

	getJSON: function( url, data, callback ) {
		return jQuery.get( url, data, callback, "json" );
	},

	getScript: function( url, callback ) {
		return jQuery.get( url, undefined, callback, "script" );
	}
} );

jQuery.each( [ "get", "post" ], function( i, method ) {
	jQuery[ method ] = function( url, data, callback, type ) {

		// Shift arguments if data argument was omitted
		if ( jQuery.isFunction( data ) ) {
			type = type || callback;
			callback = data;
			data = undefined;
		}

		// The url can be an options object (which then must have .url)
		return jQuery.ajax( jQuery.extend( {
			url: url,
			type: method,
			dataType: type,
			data: data,
			success: callback
		}, jQuery.isPlainObject( url ) && url ) );
	};
} );


jQuery._evalUrl = function( url ) {
	return jQuery.ajax( {
		url: url,

		// Make this explicit, since user can override this through ajaxSetup (#11264)
		type: "GET",
		dataType: "script",
		cache: true,
		async: false,
		global: false,
		"throws": true
	} );
};


jQuery.fn.extend( {
	wrapAll: function( html ) {
		var wrap;

		if ( this[ 0 ] ) {
			if ( jQuery.isFunction( html ) ) {
				html = html.call( this[ 0 ] );
			}

			// The elements to wrap the target around
			wrap = jQuery( html, this[ 0 ].ownerDocument ).eq( 0 ).clone( true );

			if ( this[ 0 ].parentNode ) {
				wrap.insertBefore( this[ 0 ] );
			}

			wrap.map( function() {
				var elem = this;

				while ( elem.firstElementChild ) {
					elem = elem.firstElementChild;
				}

				return elem;
			} ).append( this );
		}

		return this;
	},

	wrapInner: function( html ) {
		if ( jQuery.isFunction( html ) ) {
			return this.each( function( i ) {
				jQuery( this ).wrapInner( html.call( this, i ) );
			} );
		}

		return this.each( function() {
			var self = jQuery( this ),
				contents = self.contents();

			if ( contents.length ) {
				contents.wrapAll( html );

			} else {
				self.append( html );
			}
		} );
	},

	wrap: function( html ) {
		var isFunction = jQuery.isFunction( html );

		return this.each( function( i ) {
			jQuery( this ).wrapAll( isFunction ? html.call( this, i ) : html );
		} );
	},

	unwrap: function( selector ) {
		this.parent( selector ).not( "body" ).each( function() {
			jQuery( this ).replaceWith( this.childNodes );
		} );
		return this;
	}
} );


jQuery.expr.pseudos.hidden = function( elem ) {
	return !jQuery.expr.pseudos.visible( elem );
};
jQuery.expr.pseudos.visible = function( elem ) {
	return !!( elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length );
};




jQuery.ajaxSettings.xhr = function() {
	try {
		return new window.XMLHttpRequest();
	} catch ( e ) {}
};

var xhrSuccessStatus = {

		// File protocol always yields status code 0, assume 200
		0: 200,

		// Support: IE <=9 only
		// #1450: sometimes IE returns 1223 when it should be 204
		1223: 204
	},
	xhrSupported = jQuery.ajaxSettings.xhr();

support.cors = !!xhrSupported && ( "withCredentials" in xhrSupported );
support.ajax = xhrSupported = !!xhrSupported;

jQuery.ajaxTransport( function( options ) {
	var callback, errorCallback;

	// Cross domain only allowed if supported through XMLHttpRequest
	if ( support.cors || xhrSupported && !options.crossDomain ) {
		return {
			send: function( headers, complete ) {
				var i,
					xhr = options.xhr();

				xhr.open(
					options.type,
					options.url,
					options.async,
					options.username,
					options.password
				);

				// Apply custom fields if provided
				if ( options.xhrFields ) {
					for ( i in options.xhrFields ) {
						xhr[ i ] = options.xhrFields[ i ];
					}
				}

				// Override mime type if needed
				if ( options.mimeType && xhr.overrideMimeType ) {
					xhr.overrideMimeType( options.mimeType );
				}

				// X-Requested-With header
				// For cross-domain requests, seeing as conditions for a preflight are
				// akin to a jigsaw puzzle, we simply never set it to be sure.
				// (it can always be set on a per-request basis or even using ajaxSetup)
				// For same-domain requests, won't change header if already provided.
				if ( !options.crossDomain && !headers[ "X-Requested-With" ] ) {
					headers[ "X-Requested-With" ] = "XMLHttpRequest";
				}

				// Set headers
				for ( i in headers ) {
					xhr.setRequestHeader( i, headers[ i ] );
				}

				// Callback
				callback = function( type ) {
					return function() {
						if ( callback ) {
							callback = errorCallback = xhr.onload =
								xhr.onerror = xhr.onabort = xhr.onreadystatechange = null;

							if ( type === "abort" ) {
								xhr.abort();
							} else if ( type === "error" ) {

								// Support: IE <=9 only
								// On a manual native abort, IE9 throws
								// errors on any property access that is not readyState
								if ( typeof xhr.status !== "number" ) {
									complete( 0, "error" );
								} else {
									complete(

										// File: protocol always yields status 0; see #8605, #14207
										xhr.status,
										xhr.statusText
									);
								}
							} else {
								complete(
									xhrSuccessStatus[ xhr.status ] || xhr.status,
									xhr.statusText,

									// Support: IE <=9 only
									// IE9 has no XHR2 but throws on binary (trac-11426)
									// For XHR2 non-text, let the caller handle it (gh-2498)
									( xhr.responseType || "text" ) !== "text"  ||
									typeof xhr.responseText !== "string" ?
										{ binary: xhr.response } :
										{ text: xhr.responseText },
									xhr.getAllResponseHeaders()
								);
							}
						}
					};
				};

				// Listen to events
				xhr.onload = callback();
				errorCallback = xhr.onerror = callback( "error" );

				// Support: IE 9 only
				// Use onreadystatechange to replace onabort
				// to handle uncaught aborts
				if ( xhr.onabort !== undefined ) {
					xhr.onabort = errorCallback;
				} else {
					xhr.onreadystatechange = function() {

						// Check readyState before timeout as it changes
						if ( xhr.readyState === 4 ) {

							// Allow onerror to be called first,
							// but that will not handle a native abort
							// Also, save errorCallback to a variable
							// as xhr.onerror cannot be accessed
							window.setTimeout( function() {
								if ( callback ) {
									errorCallback();
								}
							} );
						}
					};
				}

				// Create the abort callback
				callback = callback( "abort" );

				try {

					// Do send the request (this may raise an exception)
					xhr.send( options.hasContent && options.data || null );
				} catch ( e ) {

					// #14683: Only rethrow if this hasn't been notified as an error yet
					if ( callback ) {
						throw e;
					}
				}
			},

			abort: function() {
				if ( callback ) {
					callback();
				}
			}
		};
	}
} );




// Prevent auto-execution of scripts when no explicit dataType was provided (See gh-2432)
jQuery.ajaxPrefilter( function( s ) {
	if ( s.crossDomain ) {
		s.contents.script = false;
	}
} );

// Install script dataType
jQuery.ajaxSetup( {
	accepts: {
		script: "text/javascript, application/javascript, " +
			"application/ecmascript, application/x-ecmascript"
	},
	contents: {
		script: /\b(?:java|ecma)script\b/
	},
	converters: {
		"text script": function( text ) {
			jQuery.globalEval( text );
			return text;
		}
	}
} );

// Handle cache's special case and crossDomain
jQuery.ajaxPrefilter( "script", function( s ) {
	if ( s.cache === undefined ) {
		s.cache = false;
	}
	if ( s.crossDomain ) {
		s.type = "GET";
	}
} );

// Bind script tag hack transport
jQuery.ajaxTransport( "script", function( s ) {

	// This transport only deals with cross domain requests
	if ( s.crossDomain ) {
		var script, callback;
		return {
			send: function( _, complete ) {
				script = jQuery( "<script>" ).prop( {
					charset: s.scriptCharset,
					src: s.url
				} ).on(
					"load error",
					callback = function( evt ) {
						script.remove();
						callback = null;
						if ( evt ) {
							complete( evt.type === "error" ? 404 : 200, evt.type );
						}
					}
				);

				// Use native DOM manipulation to avoid our domManip AJAX trickery
				document.head.appendChild( script[ 0 ] );
			},
			abort: function() {
				if ( callback ) {
					callback();
				}
			}
		};
	}
} );




var oldCallbacks = [],
	rjsonp = /(=)\?(?=&|$)|\?\?/;

// Default jsonp settings
jQuery.ajaxSetup( {
	jsonp: "callback",
	jsonpCallback: function() {
		var callback = oldCallbacks.pop() || ( jQuery.expando + "_" + ( nonce++ ) );
		this[ callback ] = true;
		return callback;
	}
} );

// Detect, normalize options and install callbacks for jsonp requests
jQuery.ajaxPrefilter( "json jsonp", function( s, originalSettings, jqXHR ) {

	var callbackName, overwritten, responseContainer,
		jsonProp = s.jsonp !== false && ( rjsonp.test( s.url ) ?
			"url" :
			typeof s.data === "string" &&
				( s.contentType || "" )
					.indexOf( "application/x-www-form-urlencoded" ) === 0 &&
				rjsonp.test( s.data ) && "data"
		);

	// Handle iff the expected data type is "jsonp" or we have a parameter to set
	if ( jsonProp || s.dataTypes[ 0 ] === "jsonp" ) {

		// Get callback name, remembering preexisting value associated with it
		callbackName = s.jsonpCallback = jQuery.isFunction( s.jsonpCallback ) ?
			s.jsonpCallback() :
			s.jsonpCallback;

		// Insert callback into url or form data
		if ( jsonProp ) {
			s[ jsonProp ] = s[ jsonProp ].replace( rjsonp, "$1" + callbackName );
		} else if ( s.jsonp !== false ) {
			s.url += ( rquery.test( s.url ) ? "&" : "?" ) + s.jsonp + "=" + callbackName;
		}

		// Use data converter to retrieve json after script execution
		s.converters[ "script json" ] = function() {
			if ( !responseContainer ) {
				jQuery.error( callbackName + " was not called" );
			}
			return responseContainer[ 0 ];
		};

		// Force json dataType
		s.dataTypes[ 0 ] = "json";

		// Install callback
		overwritten = window[ callbackName ];
		window[ callbackName ] = function() {
			responseContainer = arguments;
		};

		// Clean-up function (fires after converters)
		jqXHR.always( function() {

			// If previous value didn't exist - remove it
			if ( overwritten === undefined ) {
				jQuery( window ).removeProp( callbackName );

			// Otherwise restore preexisting value
			} else {
				window[ callbackName ] = overwritten;
			}

			// Save back as free
			if ( s[ callbackName ] ) {

				// Make sure that re-using the options doesn't screw things around
				s.jsonpCallback = originalSettings.jsonpCallback;

				// Save the callback name for future use
				oldCallbacks.push( callbackName );
			}

			// Call if it was a function and we have a response
			if ( responseContainer && jQuery.isFunction( overwritten ) ) {
				overwritten( responseContainer[ 0 ] );
			}

			responseContainer = overwritten = undefined;
		} );

		// Delegate to script
		return "script";
	}
} );




// Support: Safari 8 only
// In Safari 8 documents created via document.implementation.createHTMLDocument
// collapse sibling forms: the second one becomes a child of the first one.
// Because of that, this security measure has to be disabled in Safari 8.
// https://bugs.webkit.org/show_bug.cgi?id=137337
support.createHTMLDocument = ( function() {
	var body = document.implementation.createHTMLDocument( "" ).body;
	body.innerHTML = "<form></form><form></form>";
	return body.childNodes.length === 2;
} )();


// Argument "data" should be string of html
// context (optional): If specified, the fragment will be created in this context,
// defaults to document
// keepScripts (optional): If true, will include scripts passed in the html string
jQuery.parseHTML = function( data, context, keepScripts ) {
	if ( typeof data !== "string" ) {
		return [];
	}
	if ( typeof context === "boolean" ) {
		keepScripts = context;
		context = false;
	}

	var base, parsed, scripts;

	if ( !context ) {

		// Stop scripts or inline event handlers from being executed immediately
		// by using document.implementation
		if ( support.createHTMLDocument ) {
			context = document.implementation.createHTMLDocument( "" );

			// Set the base href for the created document
			// so any parsed elements with URLs
			// are based on the document's URL (gh-2965)
			base = context.createElement( "base" );
			base.href = document.location.href;
			context.head.appendChild( base );
		} else {
			context = document;
		}
	}

	parsed = rsingleTag.exec( data );
	scripts = !keepScripts && [];

	// Single tag
	if ( parsed ) {
		return [ context.createElement( parsed[ 1 ] ) ];
	}

	parsed = buildFragment( [ data ], context, scripts );

	if ( scripts && scripts.length ) {
		jQuery( scripts ).remove();
	}

	return jQuery.merge( [], parsed.childNodes );
};


/**
 * Load a url into a page
 */
jQuery.fn.load = function( url, params, callback ) {
	var selector, type, response,
		self = this,
		off = url.indexOf( " " );

	if ( off > -1 ) {
		selector = stripAndCollapse( url.slice( off ) );
		url = url.slice( 0, off );
	}

	// If it's a function
	if ( jQuery.isFunction( params ) ) {

		// We assume that it's the callback
		callback = params;
		params = undefined;

	// Otherwise, build a param string
	} else if ( params && typeof params === "object" ) {
		type = "POST";
	}

	// If we have elements to modify, make the request
	if ( self.length > 0 ) {
		jQuery.ajax( {
			url: url,

			// If "type" variable is undefined, then "GET" method will be used.
			// Make value of this field explicit since
			// user can override it through ajaxSetup method
			type: type || "GET",
			dataType: "html",
			data: params
		} ).done( function( responseText ) {

			// Save response for use in complete callback
			response = arguments;

			self.html( selector ?

				// If a selector was specified, locate the right elements in a dummy div
				// Exclude scripts to avoid IE 'Permission Denied' errors
				jQuery( "<div>" ).append( jQuery.parseHTML( responseText ) ).find( selector ) :

				// Otherwise use the full result
				responseText );

		// If the request succeeds, this function gets "data", "status", "jqXHR"
		// but they are ignored because response was set above.
		// If it fails, this function gets "jqXHR", "status", "error"
		} ).always( callback && function( jqXHR, status ) {
			self.each( function() {
				callback.apply( this, response || [ jqXHR.responseText, status, jqXHR ] );
			} );
		} );
	}

	return this;
};




// Attach a bunch of functions for handling common AJAX events
jQuery.each( [
	"ajaxStart",
	"ajaxStop",
	"ajaxComplete",
	"ajaxError",
	"ajaxSuccess",
	"ajaxSend"
], function( i, type ) {
	jQuery.fn[ type ] = function( fn ) {
		return this.on( type, fn );
	};
} );




jQuery.expr.pseudos.animated = function( elem ) {
	return jQuery.grep( jQuery.timers, function( fn ) {
		return elem === fn.elem;
	} ).length;
};




jQuery.offset = {
	setOffset: function( elem, options, i ) {
		var curPosition, curLeft, curCSSTop, curTop, curOffset, curCSSLeft, calculatePosition,
			position = jQuery.css( elem, "position" ),
			curElem = jQuery( elem ),
			props = {};

		// Set position first, in-case top/left are set even on static elem
		if ( position === "static" ) {
			elem.style.position = "relative";
		}

		curOffset = curElem.offset();
		curCSSTop = jQuery.css( elem, "top" );
		curCSSLeft = jQuery.css( elem, "left" );
		calculatePosition = ( position === "absolute" || position === "fixed" ) &&
			( curCSSTop + curCSSLeft ).indexOf( "auto" ) > -1;

		// Need to be able to calculate position if either
		// top or left is auto and position is either absolute or fixed
		if ( calculatePosition ) {
			curPosition = curElem.position();
			curTop = curPosition.top;
			curLeft = curPosition.left;

		} else {
			curTop = parseFloat( curCSSTop ) || 0;
			curLeft = parseFloat( curCSSLeft ) || 0;
		}

		if ( jQuery.isFunction( options ) ) {

			// Use jQuery.extend here to allow modification of coordinates argument (gh-1848)
			options = options.call( elem, i, jQuery.extend( {}, curOffset ) );
		}

		if ( options.top != null ) {
			props.top = ( options.top - curOffset.top ) + curTop;
		}
		if ( options.left != null ) {
			props.left = ( options.left - curOffset.left ) + curLeft;
		}

		if ( "using" in options ) {
			options.using.call( elem, props );

		} else {
			curElem.css( props );
		}
	}
};

jQuery.fn.extend( {
	offset: function( options ) {

		// Preserve chaining for setter
		if ( arguments.length ) {
			return options === undefined ?
				this :
				this.each( function( i ) {
					jQuery.offset.setOffset( this, options, i );
				} );
		}

		var doc, docElem, rect, win,
			elem = this[ 0 ];

		if ( !elem ) {
			return;
		}

		// Return zeros for disconnected and hidden (display: none) elements (gh-2310)
		// Support: IE <=11 only
		// Running getBoundingClientRect on a
		// disconnected node in IE throws an error
		if ( !elem.getClientRects().length ) {
			return { top: 0, left: 0 };
		}

		rect = elem.getBoundingClientRect();

		doc = elem.ownerDocument;
		docElem = doc.documentElement;
		win = doc.defaultView;

		return {
			top: rect.top + win.pageYOffset - docElem.clientTop,
			left: rect.left + win.pageXOffset - docElem.clientLeft
		};
	},

	position: function() {
		if ( !this[ 0 ] ) {
			return;
		}

		var offsetParent, offset,
			elem = this[ 0 ],
			parentOffset = { top: 0, left: 0 };

		// Fixed elements are offset from window (parentOffset = {top:0, left: 0},
		// because it is its only offset parent
		if ( jQuery.css( elem, "position" ) === "fixed" ) {

			// Assume getBoundingClientRect is there when computed position is fixed
			offset = elem.getBoundingClientRect();

		} else {

			// Get *real* offsetParent
			offsetParent = this.offsetParent();

			// Get correct offsets
			offset = this.offset();
			if ( !nodeName( offsetParent[ 0 ], "html" ) ) {
				parentOffset = offsetParent.offset();
			}

			// Add offsetParent borders
			parentOffset = {
				top: parentOffset.top + jQuery.css( offsetParent[ 0 ], "borderTopWidth", true ),
				left: parentOffset.left + jQuery.css( offsetParent[ 0 ], "borderLeftWidth", true )
			};
		}

		// Subtract parent offsets and element margins
		return {
			top: offset.top - parentOffset.top - jQuery.css( elem, "marginTop", true ),
			left: offset.left - parentOffset.left - jQuery.css( elem, "marginLeft", true )
		};
	},

	// This method will return documentElement in the following cases:
	// 1) For the element inside the iframe without offsetParent, this method will return
	//    documentElement of the parent window
	// 2) For the hidden or detached element
	// 3) For body or html element, i.e. in case of the html node - it will return itself
	//
	// but those exceptions were never presented as a real life use-cases
	// and might be considered as more preferable results.
	//
	// This logic, however, is not guaranteed and can change at any point in the future
	offsetParent: function() {
		return this.map( function() {
			var offsetParent = this.offsetParent;

			while ( offsetParent && jQuery.css( offsetParent, "position" ) === "static" ) {
				offsetParent = offsetParent.offsetParent;
			}

			return offsetParent || documentElement;
		} );
	}
} );

// Create scrollLeft and scrollTop methods
jQuery.each( { scrollLeft: "pageXOffset", scrollTop: "pageYOffset" }, function( method, prop ) {
	var top = "pageYOffset" === prop;

	jQuery.fn[ method ] = function( val ) {
		return access( this, function( elem, method, val ) {

			// Coalesce documents and windows
			var win;
			if ( jQuery.isWindow( elem ) ) {
				win = elem;
			} else if ( elem.nodeType === 9 ) {
				win = elem.defaultView;
			}

			if ( val === undefined ) {
				return win ? win[ prop ] : elem[ method ];
			}

			if ( win ) {
				win.scrollTo(
					!top ? val : win.pageXOffset,
					top ? val : win.pageYOffset
				);

			} else {
				elem[ method ] = val;
			}
		}, method, val, arguments.length );
	};
} );

// Support: Safari <=7 - 9.1, Chrome <=37 - 49
// Add the top/left cssHooks using jQuery.fn.position
// Webkit bug: https://bugs.webkit.org/show_bug.cgi?id=29084
// Blink bug: https://bugs.chromium.org/p/chromium/issues/detail?id=589347
// getComputedStyle returns percent when specified for top/left/bottom/right;
// rather than make the css module depend on the offset module, just check for it here
jQuery.each( [ "top", "left" ], function( i, prop ) {
	jQuery.cssHooks[ prop ] = addGetHookIf( support.pixelPosition,
		function( elem, computed ) {
			if ( computed ) {
				computed = curCSS( elem, prop );

				// If curCSS returns percentage, fallback to offset
				return rnumnonpx.test( computed ) ?
					jQuery( elem ).position()[ prop ] + "px" :
					computed;
			}
		}
	);
} );


// Create innerHeight, innerWidth, height, width, outerHeight and outerWidth methods
jQuery.each( { Height: "height", Width: "width" }, function( name, type ) {
	jQuery.each( { padding: "inner" + name, content: type, "": "outer" + name },
		function( defaultExtra, funcName ) {

		// Margin is only for outerHeight, outerWidth
		jQuery.fn[ funcName ] = function( margin, value ) {
			var chainable = arguments.length && ( defaultExtra || typeof margin !== "boolean" ),
				extra = defaultExtra || ( margin === true || value === true ? "margin" : "border" );

			return access( this, function( elem, type, value ) {
				var doc;

				if ( jQuery.isWindow( elem ) ) {

					// $( window ).outerWidth/Height return w/h including scrollbars (gh-1729)
					return funcName.indexOf( "outer" ) === 0 ?
						elem[ "inner" + name ] :
						elem.document.documentElement[ "client" + name ];
				}

				// Get document width or height
				if ( elem.nodeType === 9 ) {
					doc = elem.documentElement;

					// Either scroll[Width/Height] or offset[Width/Height] or client[Width/Height],
					// whichever is greatest
					return Math.max(
						elem.body[ "scroll" + name ], doc[ "scroll" + name ],
						elem.body[ "offset" + name ], doc[ "offset" + name ],
						doc[ "client" + name ]
					);
				}

				return value === undefined ?

					// Get width or height on the element, requesting but not forcing parseFloat
					jQuery.css( elem, type, extra ) :

					// Set width or height on the element
					jQuery.style( elem, type, value, extra );
			}, type, chainable ? margin : undefined, chainable );
		};
	} );
} );


jQuery.fn.extend( {

	bind: function( types, data, fn ) {
		return this.on( types, null, data, fn );
	},
	unbind: function( types, fn ) {
		return this.off( types, null, fn );
	},

	delegate: function( selector, types, data, fn ) {
		return this.on( types, selector, data, fn );
	},
	undelegate: function( selector, types, fn ) {

		// ( namespace ) or ( selector, types [, fn] )
		return arguments.length === 1 ?
			this.off( selector, "**" ) :
			this.off( types, selector || "**", fn );
	}
} );

jQuery.holdReady = function( hold ) {
	if ( hold ) {
		jQuery.readyWait++;
	} else {
		jQuery.ready( true );
	}
};
jQuery.isArray = Array.isArray;
jQuery.parseJSON = JSON.parse;
jQuery.nodeName = nodeName;




// Register as a named AMD module, since jQuery can be concatenated with other
// files that may use define, but not via a proper concatenation script that
// understands anonymous AMD modules. A named AMD is safest and most robust
// way to register. Lowercase jquery is used because AMD module names are
// derived from file names, and jQuery is normally delivered in a lowercase
// file name. Do this after creating the global so that if an AMD module wants
// to call noConflict to hide this version of jQuery, it will work.

// Note that for maximum portability, libraries that are not jQuery should
// declare themselves as anonymous modules, and avoid setting a global if an
// AMD loader is present. jQuery is a special case. For more information, see
// https://github.com/jrburke/requirejs/wiki/Updating-existing-libraries#wiki-anon

if ( typeof define === "function" && define.amd ) {
	define( "jquery", [], function() {
		return jQuery;
	} );
}




var

	// Map over jQuery in case of overwrite
	_jQuery = window.jQuery,

	// Map over the $ in case of overwrite
	_$ = window.$;

jQuery.noConflict = function( deep ) {
	if ( window.$ === jQuery ) {
		window.$ = _$;
	}

	if ( deep && window.jQuery === jQuery ) {
		window.jQuery = _jQuery;
	}

	return jQuery;
};

// Expose jQuery and $ identifiers, even in AMD
// (#7102#comment:10, https://github.com/jquery/jquery/pull/557)
// and CommonJS for browser emulators (#13566)
if ( !noGlobal ) {
	window.jQuery = window.$ = jQuery;
}




return jQuery;
} );

/**
 * @license AngularJS v1.6.3
 * (c) 2010-2017 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window) {'use strict';

/**
 * @description
 *
 * This object provides a utility for producing rich Error messages within
 * Angular. It can be called as follows:
 *
 * var exampleMinErr = minErr('example');
 * throw exampleMinErr('one', 'This {0} is {1}', foo, bar);
 *
 * The above creates an instance of minErr in the example namespace. The
 * resulting error will have a namespaced error code of example.one.  The
 * resulting error will replace {0} with the value of foo, and {1} with the
 * value of bar. The object is not restricted in the number of arguments it can
 * take.
 *
 * If fewer arguments are specified than necessary for interpolation, the extra
 * interpolation markers will be preserved in the final string.
 *
 * Since data will be parsed statically during a build step, some restrictions
 * are applied with respect to how minErr instances are created and called.
 * Instances should have names of the form namespaceMinErr for a minErr created
 * using minErr('namespace') . Error codes, namespaces and template strings
 * should all be static strings, not variables or general expressions.
 *
 * @param {string} module The namespace to use for the new minErr instance.
 * @param {function} ErrorConstructor Custom error constructor to be instantiated when returning
 *   error from returned function, for cases when a particular type of error is useful.
 * @returns {function(code:string, template:string, ...templateArgs): Error} minErr instance
 */

function minErr(module, ErrorConstructor) {
  ErrorConstructor = ErrorConstructor || Error;
  return function() {
    var code = arguments[0],
      template = arguments[1],
      message = '[' + (module ? module + ':' : '') + code + '] ',
      templateArgs = sliceArgs(arguments, 2).map(function(arg) {
        return toDebugString(arg, minErrConfig.objectMaxDepth);
      }),
      paramPrefix, i;

    message += template.replace(/\{\d+\}/g, function(match) {
      var index = +match.slice(1, -1);

      if (index < templateArgs.length) {
        return templateArgs[index];
      }

      return match;
    });

    message += '\nhttp://errors.angularjs.org/1.6.3/' +
      (module ? module + '/' : '') + code;

    for (i = 0, paramPrefix = '?'; i < templateArgs.length; i++, paramPrefix = '&') {
      message += paramPrefix + 'p' + i + '=' + encodeURIComponent(templateArgs[i]);
    }

    return new ErrorConstructor(message);
  };
}

/* We need to tell ESLint what variables are being exported */
/* exported
  angular,
  msie,
  jqLite,
  jQuery,
  slice,
  splice,
  push,
  toString,
  minErrConfig,
  errorHandlingConfig,
  isValidObjectMaxDepth,
  ngMinErr,
  angularModule,
  uid,
  REGEX_STRING_REGEXP,
  VALIDITY_STATE_PROPERTY,

  lowercase,
  uppercase,
  manualLowercase,
  manualUppercase,
  nodeName_,
  isArrayLike,
  forEach,
  forEachSorted,
  reverseParams,
  nextUid,
  setHashKey,
  extend,
  toInt,
  inherit,
  merge,
  noop,
  identity,
  valueFn,
  isUndefined,
  isDefined,
  isObject,
  isBlankObject,
  isString,
  isNumber,
  isNumberNaN,
  isDate,
  isArray,
  isFunction,
  isRegExp,
  isWindow,
  isScope,
  isFile,
  isFormData,
  isBlob,
  isBoolean,
  isPromiseLike,
  trim,
  escapeForRegexp,
  isElement,
  makeMap,
  includes,
  arrayRemove,
  copy,
  equals,
  csp,
  jq,
  concat,
  sliceArgs,
  bind,
  toJsonReplacer,
  toJson,
  fromJson,
  convertTimezoneToLocal,
  timezoneToOffset,
  startingTag,
  tryDecodeURIComponent,
  parseKeyValue,
  toKeyValue,
  encodeUriSegment,
  encodeUriQuery,
  angularInit,
  bootstrap,
  getTestability,
  snake_case,
  bindJQuery,
  assertArg,
  assertArgFn,
  assertNotHasOwnProperty,
  getter,
  getBlockNodes,
  hasOwnProperty,
  createMap,
  stringify,

  NODE_TYPE_ELEMENT,
  NODE_TYPE_ATTRIBUTE,
  NODE_TYPE_TEXT,
  NODE_TYPE_COMMENT,
  NODE_TYPE_DOCUMENT,
  NODE_TYPE_DOCUMENT_FRAGMENT
*/

////////////////////////////////////

/**
 * @ngdoc module
 * @name ng
 * @module ng
 * @installation
 * @description
 *
 * # ng (core module)
 * The ng module is loaded by default when an AngularJS application is started. The module itself
 * contains the essential components for an AngularJS application to function. The table below
 * lists a high level breakdown of each of the services/factories, filters, directives and testing
 * components available within this core module.
 *
 * <div doc-module-components="ng"></div>
 */

var REGEX_STRING_REGEXP = /^\/(.+)\/([a-z]*)$/;

// The name of a form control's ValidityState property.
// This is used so that it's possible for internal tests to create mock ValidityStates.
var VALIDITY_STATE_PROPERTY = 'validity';


var hasOwnProperty = Object.prototype.hasOwnProperty;

var minErrConfig = {
  objectMaxDepth: 5
};

 /**
 * @ngdoc function
 * @name angular.errorHandlingConfig
 * @module ng
 * @kind function
 *
 * @description
 * Configure several aspects of error handling in AngularJS if used as a setter or return the
 * current configuration if used as a getter. The following options are supported:
 *
 * - **objectMaxDepth**: The maximum depth to which objects are traversed when stringified for error messages.
 *
 * Omitted or undefined options will leave the corresponding configuration values unchanged.
 *
 * @param {Object=} config - The configuration object. May only contain the options that need to be
 *     updated. Supported keys:
 *
 * * `objectMaxDepth`  **{Number}** - The max depth for stringifying objects. Setting to a
 *   non-positive or non-numeric value, removes the max depth limit.
 *   Default: 5
 */
function errorHandlingConfig(config) {
  if (isObject(config)) {
    if (isDefined(config.objectMaxDepth)) {
      minErrConfig.objectMaxDepth = isValidObjectMaxDepth(config.objectMaxDepth) ? config.objectMaxDepth : NaN;
    }
  } else {
    return minErrConfig;
  }
}

/**
 * @private
 * @param {Number} maxDepth
 * @return {boolean}
 */
function isValidObjectMaxDepth(maxDepth) {
  return isNumber(maxDepth) && maxDepth > 0;
}

/**
 * @ngdoc function
 * @name angular.lowercase
 * @module ng
 * @kind function
 *
 * @deprecated
 * sinceVersion="1.5.0"
 * removeVersion="1.7.0"
 * Use [String.prototype.toLowerCase](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/String/toLowerCase) instead.
 *
 * @description Converts the specified string to lowercase.
 * @param {string} string String to be converted to lowercase.
 * @returns {string} Lowercased string.
 */
var lowercase = function(string) {return isString(string) ? string.toLowerCase() : string;};

/**
 * @ngdoc function
 * @name angular.uppercase
 * @module ng
 * @kind function
 *
 * @deprecated
 * sinceVersion="1.5.0"
 * removeVersion="1.7.0"
 * Use [String.prototype.toUpperCase](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/String/toUpperCase) instead.
 *
 * @description Converts the specified string to uppercase.
 * @param {string} string String to be converted to uppercase.
 * @returns {string} Uppercased string.
 */
var uppercase = function(string) {return isString(string) ? string.toUpperCase() : string;};


var manualLowercase = function(s) {
  /* eslint-disable no-bitwise */
  return isString(s)
      ? s.replace(/[A-Z]/g, function(ch) {return String.fromCharCode(ch.charCodeAt(0) | 32);})
      : s;
  /* eslint-enable */
};
var manualUppercase = function(s) {
  /* eslint-disable no-bitwise */
  return isString(s)
      ? s.replace(/[a-z]/g, function(ch) {return String.fromCharCode(ch.charCodeAt(0) & ~32);})
      : s;
  /* eslint-enable */
};


// String#toLowerCase and String#toUpperCase don't produce correct results in browsers with Turkish
// locale, for this reason we need to detect this case and redefine lowercase/uppercase methods
// with correct but slower alternatives. See https://github.com/angular/angular.js/issues/11387
if ('i' !== 'I'.toLowerCase()) {
  lowercase = manualLowercase;
  uppercase = manualUppercase;
}


var
    msie,             // holds major version number for IE, or NaN if UA is not IE.
    jqLite,           // delay binding since jQuery could be loaded after us.
    jQuery,           // delay binding
    slice             = [].slice,
    splice            = [].splice,
    push              = [].push,
    toString          = Object.prototype.toString,
    getPrototypeOf    = Object.getPrototypeOf,
    ngMinErr          = minErr('ng'),

    /** @name angular */
    angular           = window.angular || (window.angular = {}),
    angularModule,
    uid               = 0;

// Support: IE 9-11 only
/**
 * documentMode is an IE-only property
 * http://msdn.microsoft.com/en-us/library/ie/cc196988(v=vs.85).aspx
 */
msie = window.document.documentMode;


/**
 * @private
 * @param {*} obj
 * @return {boolean} Returns true if `obj` is an array or array-like object (NodeList, Arguments,
 *                   String ...)
 */
function isArrayLike(obj) {

  // `null`, `undefined` and `window` are not array-like
  if (obj == null || isWindow(obj)) return false;

  // arrays, strings and jQuery/jqLite objects are array like
  // * jqLite is either the jQuery or jqLite constructor function
  // * we have to check the existence of jqLite first as this method is called
  //   via the forEach method when constructing the jqLite object in the first place
  if (isArray(obj) || isString(obj) || (jqLite && obj instanceof jqLite)) return true;

  // Support: iOS 8.2 (not reproducible in simulator)
  // "length" in obj used to prevent JIT error (gh-11508)
  var length = 'length' in Object(obj) && obj.length;

  // NodeList objects (with `item` method) and
  // other objects with suitable length characteristics are array-like
  return isNumber(length) &&
    (length >= 0 && ((length - 1) in obj || obj instanceof Array) || typeof obj.item === 'function');

}

/**
 * @ngdoc function
 * @name angular.forEach
 * @module ng
 * @kind function
 *
 * @description
 * Invokes the `iterator` function once for each item in `obj` collection, which can be either an
 * object or an array. The `iterator` function is invoked with `iterator(value, key, obj)`, where `value`
 * is the value of an object property or an array element, `key` is the object property key or
 * array element index and obj is the `obj` itself. Specifying a `context` for the function is optional.
 *
 * It is worth noting that `.forEach` does not iterate over inherited properties because it filters
 * using the `hasOwnProperty` method.
 *
 * Unlike ES262's
 * [Array.prototype.forEach](http://www.ecma-international.org/ecma-262/5.1/#sec-15.4.4.18),
 * providing 'undefined' or 'null' values for `obj` will not throw a TypeError, but rather just
 * return the value provided.
 *
   ```js
     var values = {name: 'misko', gender: 'male'};
     var log = [];
     angular.forEach(values, function(value, key) {
       this.push(key + ': ' + value);
     }, log);
     expect(log).toEqual(['name: misko', 'gender: male']);
   ```
 *
 * @param {Object|Array} obj Object to iterate over.
 * @param {Function} iterator Iterator function.
 * @param {Object=} context Object to become context (`this`) for the iterator function.
 * @returns {Object|Array} Reference to `obj`.
 */

function forEach(obj, iterator, context) {
  var key, length;
  if (obj) {
    if (isFunction(obj)) {
      for (key in obj) {
        if (key !== 'prototype' && key !== 'length' && key !== 'name' && obj.hasOwnProperty(key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else if (isArray(obj) || isArrayLike(obj)) {
      var isPrimitive = typeof obj !== 'object';
      for (key = 0, length = obj.length; key < length; key++) {
        if (isPrimitive || key in obj) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else if (obj.forEach && obj.forEach !== forEach) {
        obj.forEach(iterator, context, obj);
    } else if (isBlankObject(obj)) {
      // createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
      for (key in obj) {
        iterator.call(context, obj[key], key, obj);
      }
    } else if (typeof obj.hasOwnProperty === 'function') {
      // Slow path for objects inheriting Object.prototype, hasOwnProperty check needed
      for (key in obj) {
        if (obj.hasOwnProperty(key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else {
      // Slow path for objects which do not have a method `hasOwnProperty`
      for (key in obj) {
        if (hasOwnProperty.call(obj, key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    }
  }
  return obj;
}

function forEachSorted(obj, iterator, context) {
  var keys = Object.keys(obj).sort();
  for (var i = 0; i < keys.length; i++) {
    iterator.call(context, obj[keys[i]], keys[i]);
  }
  return keys;
}


/**
 * when using forEach the params are value, key, but it is often useful to have key, value.
 * @param {function(string, *)} iteratorFn
 * @returns {function(*, string)}
 */
function reverseParams(iteratorFn) {
  return function(value, key) {iteratorFn(key, value);};
}

/**
 * A consistent way of creating unique IDs in angular.
 *
 * Using simple numbers allows us to generate 28.6 million unique ids per second for 10 years before
 * we hit number precision issues in JavaScript.
 *
 * Math.pow(2,53) / 60 / 60 / 24 / 365 / 10 = 28.6M
 *
 * @returns {number} an unique alpha-numeric string
 */
function nextUid() {
  return ++uid;
}


/**
 * Set or clear the hashkey for an object.
 * @param obj object
 * @param h the hashkey (!truthy to delete the hashkey)
 */
function setHashKey(obj, h) {
  if (h) {
    obj.$$hashKey = h;
  } else {
    delete obj.$$hashKey;
  }
}


function baseExtend(dst, objs, deep) {
  var h = dst.$$hashKey;

  for (var i = 0, ii = objs.length; i < ii; ++i) {
    var obj = objs[i];
    if (!isObject(obj) && !isFunction(obj)) continue;
    var keys = Object.keys(obj);
    for (var j = 0, jj = keys.length; j < jj; j++) {
      var key = keys[j];
      var src = obj[key];

      if (deep && isObject(src)) {
        if (isDate(src)) {
          dst[key] = new Date(src.valueOf());
        } else if (isRegExp(src)) {
          dst[key] = new RegExp(src);
        } else if (src.nodeName) {
          dst[key] = src.cloneNode(true);
        } else if (isElement(src)) {
          dst[key] = src.clone();
        } else {
          if (!isObject(dst[key])) dst[key] = isArray(src) ? [] : {};
          baseExtend(dst[key], [src], true);
        }
      } else {
        dst[key] = src;
      }
    }
  }

  setHashKey(dst, h);
  return dst;
}

/**
 * @ngdoc function
 * @name angular.extend
 * @module ng
 * @kind function
 *
 * @description
 * Extends the destination object `dst` by copying own enumerable properties from the `src` object(s)
 * to `dst`. You can specify multiple `src` objects. If you want to preserve original objects, you can do so
 * by passing an empty object as the target: `var object = angular.extend({}, object1, object2)`.
 *
 * **Note:** Keep in mind that `angular.extend` does not support recursive merge (deep copy). Use
 * {@link angular.merge} for this.
 *
 * @param {Object} dst Destination object.
 * @param {...Object} src Source object(s).
 * @returns {Object} Reference to `dst`.
 */
function extend(dst) {
  return baseExtend(dst, slice.call(arguments, 1), false);
}


/**
* @ngdoc function
* @name angular.merge
* @module ng
* @kind function
*
* @description
* Deeply extends the destination object `dst` by copying own enumerable properties from the `src` object(s)
* to `dst`. You can specify multiple `src` objects. If you want to preserve original objects, you can do so
* by passing an empty object as the target: `var object = angular.merge({}, object1, object2)`.
*
* Unlike {@link angular.extend extend()}, `merge()` recursively descends into object properties of source
* objects, performing a deep copy.
*
* @param {Object} dst Destination object.
* @param {...Object} src Source object(s).
* @returns {Object} Reference to `dst`.
*/
function merge(dst) {
  return baseExtend(dst, slice.call(arguments, 1), true);
}



function toInt(str) {
  return parseInt(str, 10);
}

var isNumberNaN = Number.isNaN || function isNumberNaN(num) {
  // eslint-disable-next-line no-self-compare
  return num !== num;
};


function inherit(parent, extra) {
  return extend(Object.create(parent), extra);
}

/**
 * @ngdoc function
 * @name angular.noop
 * @module ng
 * @kind function
 *
 * @description
 * A function that performs no operations. This function can be useful when writing code in the
 * functional style.
   ```js
     function foo(callback) {
       var result = calculateResult();
       (callback || angular.noop)(result);
     }
   ```
 */
function noop() {}
noop.$inject = [];


/**
 * @ngdoc function
 * @name angular.identity
 * @module ng
 * @kind function
 *
 * @description
 * A function that returns its first argument. This function is useful when writing code in the
 * functional style.
 *
   ```js
   function transformer(transformationFn, value) {
     return (transformationFn || angular.identity)(value);
   };

   // E.g.
   function getResult(fn, input) {
     return (fn || angular.identity)(input);
   };

   getResult(function(n) { return n * 2; }, 21);   // returns 42
   getResult(null, 21);                            // returns 21
   getResult(undefined, 21);                       // returns 21
   ```
 *
 * @param {*} value to be returned.
 * @returns {*} the value passed in.
 */
function identity($) {return $;}
identity.$inject = [];


function valueFn(value) {return function valueRef() {return value;};}

function hasCustomToString(obj) {
  return isFunction(obj.toString) && obj.toString !== toString;
}


/**
 * @ngdoc function
 * @name angular.isUndefined
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is undefined.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is undefined.
 */
function isUndefined(value) {return typeof value === 'undefined';}


/**
 * @ngdoc function
 * @name angular.isDefined
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is defined.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is defined.
 */
function isDefined(value) {return typeof value !== 'undefined';}


/**
 * @ngdoc function
 * @name angular.isObject
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is an `Object`. Unlike `typeof` in JavaScript, `null`s are not
 * considered to be objects. Note that JavaScript arrays are objects.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is an `Object` but not `null`.
 */
function isObject(value) {
  // http://jsperf.com/isobject4
  return value !== null && typeof value === 'object';
}


/**
 * Determine if a value is an object with a null prototype
 *
 * @returns {boolean} True if `value` is an `Object` with a null prototype
 */
function isBlankObject(value) {
  return value !== null && typeof value === 'object' && !getPrototypeOf(value);
}


/**
 * @ngdoc function
 * @name angular.isString
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a `String`.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `String`.
 */
function isString(value) {return typeof value === 'string';}


/**
 * @ngdoc function
 * @name angular.isNumber
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a `Number`.
 *
 * This includes the "special" numbers `NaN`, `+Infinity` and `-Infinity`.
 *
 * If you wish to exclude these then you can use the native
 * [`isFinite'](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/isFinite)
 * method.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Number`.
 */
function isNumber(value) {return typeof value === 'number';}


/**
 * @ngdoc function
 * @name angular.isDate
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a value is a date.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Date`.
 */
function isDate(value) {
  return toString.call(value) === '[object Date]';
}


/**
 * @ngdoc function
 * @name angular.isArray
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is an `Array`. Alias of Array.isArray.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is an `Array`.
 */
var isArray = Array.isArray;

/**
 * @ngdoc function
 * @name angular.isFunction
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a `Function`.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Function`.
 */
function isFunction(value) {return typeof value === 'function';}


/**
 * Determines if a value is a regular expression object.
 *
 * @private
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `RegExp`.
 */
function isRegExp(value) {
  return toString.call(value) === '[object RegExp]';
}


/**
 * Checks if `obj` is a window object.
 *
 * @private
 * @param {*} obj Object to check
 * @returns {boolean} True if `obj` is a window obj.
 */
function isWindow(obj) {
  return obj && obj.window === obj;
}


function isScope(obj) {
  return obj && obj.$evalAsync && obj.$watch;
}


function isFile(obj) {
  return toString.call(obj) === '[object File]';
}


function isFormData(obj) {
  return toString.call(obj) === '[object FormData]';
}


function isBlob(obj) {
  return toString.call(obj) === '[object Blob]';
}


function isBoolean(value) {
  return typeof value === 'boolean';
}


function isPromiseLike(obj) {
  return obj && isFunction(obj.then);
}


var TYPED_ARRAY_REGEXP = /^\[object (?:Uint8|Uint8Clamped|Uint16|Uint32|Int8|Int16|Int32|Float32|Float64)Array]$/;
function isTypedArray(value) {
  return value && isNumber(value.length) && TYPED_ARRAY_REGEXP.test(toString.call(value));
}

function isArrayBuffer(obj) {
  return toString.call(obj) === '[object ArrayBuffer]';
}


var trim = function(value) {
  return isString(value) ? value.trim() : value;
};

// Copied from:
// http://docs.closure-library.googlecode.com/git/local_closure_goog_string_string.js.source.html#line1021
// Prereq: s is a string.
var escapeForRegexp = function(s) {
  return s
    .replace(/([-()[\]{}+?*.$^|,:#<!\\])/g, '\\$1')
    // eslint-disable-next-line no-control-regex
    .replace(/\x08/g, '\\x08');
};


/**
 * @ngdoc function
 * @name angular.isElement
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a DOM element (or wrapped jQuery element).
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a DOM element (or wrapped jQuery element).
 */
function isElement(node) {
  return !!(node &&
    (node.nodeName  // We are a direct element.
    || (node.prop && node.attr && node.find)));  // We have an on and find method part of jQuery API.
}

/**
 * @param str 'key1,key2,...'
 * @returns {object} in the form of {key1:true, key2:true, ...}
 */
function makeMap(str) {
  var obj = {}, items = str.split(','), i;
  for (i = 0; i < items.length; i++) {
    obj[items[i]] = true;
  }
  return obj;
}


function nodeName_(element) {
  return lowercase(element.nodeName || (element[0] && element[0].nodeName));
}

function includes(array, obj) {
  return Array.prototype.indexOf.call(array, obj) !== -1;
}

function arrayRemove(array, value) {
  var index = array.indexOf(value);
  if (index >= 0) {
    array.splice(index, 1);
  }
  return index;
}

/**
 * @ngdoc function
 * @name angular.copy
 * @module ng
 * @kind function
 *
 * @description
 * Creates a deep copy of `source`, which should be an object or an array.
 *
 * * If no destination is supplied, a copy of the object or array is created.
 * * If a destination is provided, all of its elements (for arrays) or properties (for objects)
 *   are deleted and then all elements/properties from the source are copied to it.
 * * If `source` is not an object or array (inc. `null` and `undefined`), `source` is returned.
 * * If `source` is identical to `destination` an exception will be thrown.
 *
 * <br />
 * <div class="alert alert-warning">
 *   Only enumerable properties are taken into account. Non-enumerable properties (both on `source`
 *   and on `destination`) will be ignored.
 * </div>
 *
 * @param {*} source The source that will be used to make a copy.
 *                   Can be any type, including primitives, `null`, and `undefined`.
 * @param {(Object|Array)=} destination Destination into which the source is copied. If
 *     provided, must be of the same type as `source`.
 * @returns {*} The copy or updated `destination`, if `destination` was specified.
 *
 * @example
  <example module="copyExample" name="angular-copy">
    <file name="index.html">
      <div ng-controller="ExampleController">
        <form novalidate class="simple-form">
          <label>Name: <input type="text" ng-model="user.name" /></label><br />
          <label>Age:  <input type="number" ng-model="user.age" /></label><br />
          Gender: <label><input type="radio" ng-model="user.gender" value="male" />male</label>
                  <label><input type="radio" ng-model="user.gender" value="female" />female</label><br />
          <button ng-click="reset()">RESET</button>
          <button ng-click="update(user)">SAVE</button>
        </form>
        <pre>form = {{user | json}}</pre>
        <pre>master = {{master | json}}</pre>
      </div>
    </file>
    <file name="script.js">
      // Module: copyExample
      angular.
        module('copyExample', []).
        controller('ExampleController', ['$scope', function($scope) {
          $scope.master = {};

          $scope.reset = function() {
            // Example with 1 argument
            $scope.user = angular.copy($scope.master);
          };

          $scope.update = function(user) {
            // Example with 2 arguments
            angular.copy(user, $scope.master);
          };

          $scope.reset();
        }]);
    </file>
  </example>
 */
function copy(source, destination, maxDepth) {
  var stackSource = [];
  var stackDest = [];
  maxDepth = isValidObjectMaxDepth(maxDepth) ? maxDepth : NaN;

  if (destination) {
    if (isTypedArray(destination) || isArrayBuffer(destination)) {
      throw ngMinErr('cpta', 'Can\'t copy! TypedArray destination cannot be mutated.');
    }
    if (source === destination) {
      throw ngMinErr('cpi', 'Can\'t copy! Source and destination are identical.');
    }

    // Empty the destination object
    if (isArray(destination)) {
      destination.length = 0;
    } else {
      forEach(destination, function(value, key) {
        if (key !== '$$hashKey') {
          delete destination[key];
        }
      });
    }

    stackSource.push(source);
    stackDest.push(destination);
    return copyRecurse(source, destination, maxDepth);
  }

  return copyElement(source, maxDepth);

  function copyRecurse(source, destination, maxDepth) {
    maxDepth--;
    if (maxDepth < 0) {
      return '...';
    }
    var h = destination.$$hashKey;
    var key;
    if (isArray(source)) {
      for (var i = 0, ii = source.length; i < ii; i++) {
        destination.push(copyElement(source[i], maxDepth));
      }
    } else if (isBlankObject(source)) {
      // createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
      for (key in source) {
        destination[key] = copyElement(source[key], maxDepth);
      }
    } else if (source && typeof source.hasOwnProperty === 'function') {
      // Slow path, which must rely on hasOwnProperty
      for (key in source) {
        if (source.hasOwnProperty(key)) {
          destination[key] = copyElement(source[key], maxDepth);
        }
      }
    } else {
      // Slowest path --- hasOwnProperty can't be called as a method
      for (key in source) {
        if (hasOwnProperty.call(source, key)) {
          destination[key] = copyElement(source[key], maxDepth);
        }
      }
    }
    setHashKey(destination, h);
    return destination;
  }

  function copyElement(source, maxDepth) {
    // Simple values
    if (!isObject(source)) {
      return source;
    }

    // Already copied values
    var index = stackSource.indexOf(source);
    if (index !== -1) {
      return stackDest[index];
    }

    if (isWindow(source) || isScope(source)) {
      throw ngMinErr('cpws',
        'Can\'t copy! Making copies of Window or Scope instances is not supported.');
    }

    var needsRecurse = false;
    var destination = copyType(source);

    if (destination === undefined) {
      destination = isArray(source) ? [] : Object.create(getPrototypeOf(source));
      needsRecurse = true;
    }

    stackSource.push(source);
    stackDest.push(destination);

    return needsRecurse
      ? copyRecurse(source, destination, maxDepth)
      : destination;
  }

  function copyType(source) {
    switch (toString.call(source)) {
      case '[object Int8Array]':
      case '[object Int16Array]':
      case '[object Int32Array]':
      case '[object Float32Array]':
      case '[object Float64Array]':
      case '[object Uint8Array]':
      case '[object Uint8ClampedArray]':
      case '[object Uint16Array]':
      case '[object Uint32Array]':
        return new source.constructor(copyElement(source.buffer), source.byteOffset, source.length);

      case '[object ArrayBuffer]':
        // Support: IE10
        if (!source.slice) {
          // If we're in this case we know the environment supports ArrayBuffer
          /* eslint-disable no-undef */
          var copied = new ArrayBuffer(source.byteLength);
          new Uint8Array(copied).set(new Uint8Array(source));
          /* eslint-enable */
          return copied;
        }
        return source.slice(0);

      case '[object Boolean]':
      case '[object Number]':
      case '[object String]':
      case '[object Date]':
        return new source.constructor(source.valueOf());

      case '[object RegExp]':
        var re = new RegExp(source.source, source.toString().match(/[^/]*$/)[0]);
        re.lastIndex = source.lastIndex;
        return re;

      case '[object Blob]':
        return new source.constructor([source], {type: source.type});
    }

    if (isFunction(source.cloneNode)) {
      return source.cloneNode(true);
    }
  }
}


/**
 * @ngdoc function
 * @name angular.equals
 * @module ng
 * @kind function
 *
 * @description
 * Determines if two objects or two values are equivalent. Supports value types, regular
 * expressions, arrays and objects.
 *
 * Two objects or values are considered equivalent if at least one of the following is true:
 *
 * * Both objects or values pass `===` comparison.
 * * Both objects or values are of the same type and all of their properties are equal by
 *   comparing them with `angular.equals`.
 * * Both values are NaN. (In JavaScript, NaN == NaN => false. But we consider two NaN as equal)
 * * Both values represent the same regular expression (In JavaScript,
 *   /abc/ == /abc/ => false. But we consider two regular expressions as equal when their textual
 *   representation matches).
 *
 * During a property comparison, properties of `function` type and properties with names
 * that begin with `$` are ignored.
 *
 * Scope and DOMWindow objects are being compared only by identify (`===`).
 *
 * @param {*} o1 Object or value to compare.
 * @param {*} o2 Object or value to compare.
 * @returns {boolean} True if arguments are equal.
 *
 * @example
   <example module="equalsExample" name="equalsExample">
     <file name="index.html">
      <div ng-controller="ExampleController">
        <form novalidate>
          <h3>User 1</h3>
          Name: <input type="text" ng-model="user1.name">
          Age: <input type="number" ng-model="user1.age">

          <h3>User 2</h3>
          Name: <input type="text" ng-model="user2.name">
          Age: <input type="number" ng-model="user2.age">

          <div>
            <br/>
            <input type="button" value="Compare" ng-click="compare()">
          </div>
          User 1: <pre>{{user1 | json}}</pre>
          User 2: <pre>{{user2 | json}}</pre>
          Equal: <pre>{{result}}</pre>
        </form>
      </div>
    </file>
    <file name="script.js">
        angular.module('equalsExample', []).controller('ExampleController', ['$scope', function($scope) {
          $scope.user1 = {};
          $scope.user2 = {};
          $scope.compare = function() {
            $scope.result = angular.equals($scope.user1, $scope.user2);
          };
        }]);
    </file>
  </example>
 */
function equals(o1, o2) {
  if (o1 === o2) return true;
  if (o1 === null || o2 === null) return false;
  // eslint-disable-next-line no-self-compare
  if (o1 !== o1 && o2 !== o2) return true; // NaN === NaN
  var t1 = typeof o1, t2 = typeof o2, length, key, keySet;
  if (t1 === t2 && t1 === 'object') {
    if (isArray(o1)) {
      if (!isArray(o2)) return false;
      if ((length = o1.length) === o2.length) {
        for (key = 0; key < length; key++) {
          if (!equals(o1[key], o2[key])) return false;
        }
        return true;
      }
    } else if (isDate(o1)) {
      if (!isDate(o2)) return false;
      return equals(o1.getTime(), o2.getTime());
    } else if (isRegExp(o1)) {
      if (!isRegExp(o2)) return false;
      return o1.toString() === o2.toString();
    } else {
      if (isScope(o1) || isScope(o2) || isWindow(o1) || isWindow(o2) ||
        isArray(o2) || isDate(o2) || isRegExp(o2)) return false;
      keySet = createMap();
      for (key in o1) {
        if (key.charAt(0) === '$' || isFunction(o1[key])) continue;
        if (!equals(o1[key], o2[key])) return false;
        keySet[key] = true;
      }
      for (key in o2) {
        if (!(key in keySet) &&
            key.charAt(0) !== '$' &&
            isDefined(o2[key]) &&
            !isFunction(o2[key])) return false;
      }
      return true;
    }
  }
  return false;
}

var csp = function() {
  if (!isDefined(csp.rules)) {


    var ngCspElement = (window.document.querySelector('[ng-csp]') ||
                    window.document.querySelector('[data-ng-csp]'));

    if (ngCspElement) {
      var ngCspAttribute = ngCspElement.getAttribute('ng-csp') ||
                    ngCspElement.getAttribute('data-ng-csp');
      csp.rules = {
        noUnsafeEval: !ngCspAttribute || (ngCspAttribute.indexOf('no-unsafe-eval') !== -1),
        noInlineStyle: !ngCspAttribute || (ngCspAttribute.indexOf('no-inline-style') !== -1)
      };
    } else {
      csp.rules = {
        noUnsafeEval: noUnsafeEval(),
        noInlineStyle: false
      };
    }
  }

  return csp.rules;

  function noUnsafeEval() {
    try {
      // eslint-disable-next-line no-new, no-new-func
      new Function('');
      return false;
    } catch (e) {
      return true;
    }
  }
};

/**
 * @ngdoc directive
 * @module ng
 * @name ngJq
 *
 * @element ANY
 * @param {string=} ngJq the name of the library available under `window`
 * to be used for angular.element
 * @description
 * Use this directive to force the angular.element library.  This should be
 * used to force either jqLite by leaving ng-jq blank or setting the name of
 * the jquery variable under window (eg. jQuery).
 *
 * Since angular looks for this directive when it is loaded (doesn't wait for the
 * DOMContentLoaded event), it must be placed on an element that comes before the script
 * which loads angular. Also, only the first instance of `ng-jq` will be used and all
 * others ignored.
 *
 * @example
 * This example shows how to force jqLite using the `ngJq` directive to the `html` tag.
 ```html
 <!doctype html>
 <html ng-app ng-jq>
 ...
 ...
 </html>
 ```
 * @example
 * This example shows how to use a jQuery based library of a different name.
 * The library name must be available at the top most 'window'.
 ```html
 <!doctype html>
 <html ng-app ng-jq="jQueryLib">
 ...
 ...
 </html>
 ```
 */
var jq = function() {
  if (isDefined(jq.name_)) return jq.name_;
  var el;
  var i, ii = ngAttrPrefixes.length, prefix, name;
  for (i = 0; i < ii; ++i) {
    prefix = ngAttrPrefixes[i];
    el = window.document.querySelector('[' + prefix.replace(':', '\\:') + 'jq]');
    if (el) {
      name = el.getAttribute(prefix + 'jq');
      break;
    }
  }

  return (jq.name_ = name);
};

function concat(array1, array2, index) {
  return array1.concat(slice.call(array2, index));
}

function sliceArgs(args, startIndex) {
  return slice.call(args, startIndex || 0);
}


/**
 * @ngdoc function
 * @name angular.bind
 * @module ng
 * @kind function
 *
 * @description
 * Returns a function which calls function `fn` bound to `self` (`self` becomes the `this` for
 * `fn`). You can supply optional `args` that are prebound to the function. This feature is also
 * known as [partial application](http://en.wikipedia.org/wiki/Partial_application), as
 * distinguished from [function currying](http://en.wikipedia.org/wiki/Currying#Contrast_with_partial_function_application).
 *
 * @param {Object} self Context which `fn` should be evaluated in.
 * @param {function()} fn Function to be bound.
 * @param {...*} args Optional arguments to be prebound to the `fn` function call.
 * @returns {function()} Function that wraps the `fn` with all the specified bindings.
 */
function bind(self, fn) {
  var curryArgs = arguments.length > 2 ? sliceArgs(arguments, 2) : [];
  if (isFunction(fn) && !(fn instanceof RegExp)) {
    return curryArgs.length
      ? function() {
          return arguments.length
            ? fn.apply(self, concat(curryArgs, arguments, 0))
            : fn.apply(self, curryArgs);
        }
      : function() {
          return arguments.length
            ? fn.apply(self, arguments)
            : fn.call(self);
        };
  } else {
    // In IE, native methods are not functions so they cannot be bound (note: they don't need to be).
    return fn;
  }
}


function toJsonReplacer(key, value) {
  var val = value;

  if (typeof key === 'string' && key.charAt(0) === '$' && key.charAt(1) === '$') {
    val = undefined;
  } else if (isWindow(value)) {
    val = '$WINDOW';
  } else if (value &&  window.document === value) {
    val = '$DOCUMENT';
  } else if (isScope(value)) {
    val = '$SCOPE';
  }

  return val;
}


/**
 * @ngdoc function
 * @name angular.toJson
 * @module ng
 * @kind function
 *
 * @description
 * Serializes input into a JSON-formatted string. Properties with leading $$ characters will be
 * stripped since angular uses this notation internally.
 *
 * @param {Object|Array|Date|string|number|boolean} obj Input to be serialized into JSON.
 * @param {boolean|number} [pretty=2] If set to true, the JSON output will contain newlines and whitespace.
 *    If set to an integer, the JSON output will contain that many spaces per indentation.
 * @returns {string|undefined} JSON-ified string representing `obj`.
 * @knownIssue
 *
 * The Safari browser throws a `RangeError` instead of returning `null` when it tries to stringify a `Date`
 * object with an invalid date value. The only reliable way to prevent this is to monkeypatch the
 * `Date.prototype.toJSON` method as follows:
 *
 * ```
 * var _DatetoJSON = Date.prototype.toJSON;
 * Date.prototype.toJSON = function() {
 *   try {
 *     return _DatetoJSON.call(this);
 *   } catch(e) {
 *     if (e instanceof RangeError) {
 *       return null;
 *     }
 *     throw e;
 *   }
 * };
 * ```
 *
 * See https://github.com/angular/angular.js/pull/14221 for more information.
 */
function toJson(obj, pretty) {
  if (isUndefined(obj)) return undefined;
  if (!isNumber(pretty)) {
    pretty = pretty ? 2 : null;
  }
  return JSON.stringify(obj, toJsonReplacer, pretty);
}


/**
 * @ngdoc function
 * @name angular.fromJson
 * @module ng
 * @kind function
 *
 * @description
 * Deserializes a JSON string.
 *
 * @param {string} json JSON string to deserialize.
 * @returns {Object|Array|string|number} Deserialized JSON string.
 */
function fromJson(json) {
  return isString(json)
      ? JSON.parse(json)
      : json;
}


var ALL_COLONS = /:/g;
function timezoneToOffset(timezone, fallback) {
  // Support: IE 9-11 only, Edge 13-14+
  // IE/Edge do not "understand" colon (`:`) in timezone
  timezone = timezone.replace(ALL_COLONS, '');
  var requestedTimezoneOffset = Date.parse('Jan 01, 1970 00:00:00 ' + timezone) / 60000;
  return isNumberNaN(requestedTimezoneOffset) ? fallback : requestedTimezoneOffset;
}


function addDateMinutes(date, minutes) {
  date = new Date(date.getTime());
  date.setMinutes(date.getMinutes() + minutes);
  return date;
}


function convertTimezoneToLocal(date, timezone, reverse) {
  reverse = reverse ? -1 : 1;
  var dateTimezoneOffset = date.getTimezoneOffset();
  var timezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
  return addDateMinutes(date, reverse * (timezoneOffset - dateTimezoneOffset));
}


/**
 * @returns {string} Returns the string representation of the element.
 */
function startingTag(element) {
  element = jqLite(element).clone();
  try {
    // turns out IE does not let you set .html() on elements which
    // are not allowed to have children. So we just ignore it.
    element.empty();
  } catch (e) { /* empty */ }
  var elemHtml = jqLite('<div>').append(element).html();
  try {
    return element[0].nodeType === NODE_TYPE_TEXT ? lowercase(elemHtml) :
        elemHtml.
          match(/^(<[^>]+>)/)[1].
          replace(/^<([\w-]+)/, function(match, nodeName) {return '<' + lowercase(nodeName);});
  } catch (e) {
    return lowercase(elemHtml);
  }

}


/////////////////////////////////////////////////

/**
 * Tries to decode the URI component without throwing an exception.
 *
 * @private
 * @param str value potential URI component to check.
 * @returns {boolean} True if `value` can be decoded
 * with the decodeURIComponent function.
 */
function tryDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    // Ignore any invalid uri component.
  }
}


/**
 * Parses an escaped url query string into key-value pairs.
 * @returns {Object.<string,boolean|Array>}
 */
function parseKeyValue(/**string*/keyValue) {
  var obj = {};
  forEach((keyValue || '').split('&'), function(keyValue) {
    var splitPoint, key, val;
    if (keyValue) {
      key = keyValue = keyValue.replace(/\+/g,'%20');
      splitPoint = keyValue.indexOf('=');
      if (splitPoint !== -1) {
        key = keyValue.substring(0, splitPoint);
        val = keyValue.substring(splitPoint + 1);
      }
      key = tryDecodeURIComponent(key);
      if (isDefined(key)) {
        val = isDefined(val) ? tryDecodeURIComponent(val) : true;
        if (!hasOwnProperty.call(obj, key)) {
          obj[key] = val;
        } else if (isArray(obj[key])) {
          obj[key].push(val);
        } else {
          obj[key] = [obj[key],val];
        }
      }
    }
  });
  return obj;
}

function toKeyValue(obj) {
  var parts = [];
  forEach(obj, function(value, key) {
    if (isArray(value)) {
      forEach(value, function(arrayValue) {
        parts.push(encodeUriQuery(key, true) +
                   (arrayValue === true ? '' : '=' + encodeUriQuery(arrayValue, true)));
      });
    } else {
    parts.push(encodeUriQuery(key, true) +
               (value === true ? '' : '=' + encodeUriQuery(value, true)));
    }
  });
  return parts.length ? parts.join('&') : '';
}


/**
 * We need our custom method because encodeURIComponent is too aggressive and doesn't follow
 * http://www.ietf.org/rfc/rfc3986.txt with regards to the character set (pchar) allowed in path
 * segments:
 *    segment       = *pchar
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 */
function encodeUriSegment(val) {
  return encodeUriQuery(val, true).
             replace(/%26/gi, '&').
             replace(/%3D/gi, '=').
             replace(/%2B/gi, '+');
}


/**
 * This method is intended for encoding *key* or *value* parts of query component. We need a custom
 * method because encodeURIComponent is too aggressive and encodes stuff that doesn't have to be
 * encoded per http://tools.ietf.org/html/rfc3986:
 *    query         = *( pchar / "/" / "?" )
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 */
function encodeUriQuery(val, pctEncodeSpaces) {
  return encodeURIComponent(val).
             replace(/%40/gi, '@').
             replace(/%3A/gi, ':').
             replace(/%24/g, '$').
             replace(/%2C/gi, ',').
             replace(/%3B/gi, ';').
             replace(/%20/g, (pctEncodeSpaces ? '%20' : '+'));
}

var ngAttrPrefixes = ['ng-', 'data-ng-', 'ng:', 'x-ng-'];

function getNgAttribute(element, ngAttr) {
  var attr, i, ii = ngAttrPrefixes.length;
  for (i = 0; i < ii; ++i) {
    attr = ngAttrPrefixes[i] + ngAttr;
    if (isString(attr = element.getAttribute(attr))) {
      return attr;
    }
  }
  return null;
}

function allowAutoBootstrap(document) {
  var script = document.currentScript;

  if (!script) {
    // IE does not have `document.currentScript`
    return true;
  }

  // If the `currentScript` property has been clobbered just return false, since this indicates a probable attack
  if (!(script instanceof window.HTMLScriptElement || script instanceof window.SVGScriptElement)) {
    return false;
  }

  var attributes = script.attributes;
  var srcs = [attributes.getNamedItem('src'), attributes.getNamedItem('href'), attributes.getNamedItem('xlink:href')];

  return srcs.every(function(src) {
    if (!src) {
      return true;
    }
    if (!src.value) {
      return false;
    }

    var link = document.createElement('a');
    link.href = src.value;

    if (document.location.origin === link.origin) {
      // Same-origin resources are always allowed, even for non-whitelisted schemes.
      return true;
    }
    // Disabled bootstrapping unless angular.js was loaded from a known scheme used on the web.
    // This is to prevent angular.js bundled with browser extensions from being used to bypass the
    // content security policy in web pages and other browser extensions.
    switch (link.protocol) {
      case 'http:':
      case 'https:':
      case 'ftp:':
      case 'blob:':
      case 'file:':
      case 'data:':
        return true;
      default:
        return false;
    }
  });
}

// Cached as it has to run during loading so that document.currentScript is available.
var isAutoBootstrapAllowed = allowAutoBootstrap(window.document);

/**
 * @ngdoc directive
 * @name ngApp
 * @module ng
 *
 * @element ANY
 * @param {angular.Module} ngApp an optional application
 *   {@link angular.module module} name to load.
 * @param {boolean=} ngStrictDi if this attribute is present on the app element, the injector will be
 *   created in "strict-di" mode. This means that the application will fail to invoke functions which
 *   do not use explicit function annotation (and are thus unsuitable for minification), as described
 *   in {@link guide/di the Dependency Injection guide}, and useful debugging info will assist in
 *   tracking down the root of these bugs.
 *
 * @description
 *
 * Use this directive to **auto-bootstrap** an AngularJS application. The `ngApp` directive
 * designates the **root element** of the application and is typically placed near the root element
 * of the page - e.g. on the `<body>` or `<html>` tags.
 *
 * There are a few things to keep in mind when using `ngApp`:
 * - only one AngularJS application can be auto-bootstrapped per HTML document. The first `ngApp`
 *   found in the document will be used to define the root element to auto-bootstrap as an
 *   application. To run multiple applications in an HTML document you must manually bootstrap them using
 *   {@link angular.bootstrap} instead.
 * - AngularJS applications cannot be nested within each other.
 * - Do not use a directive that uses {@link ng.$compile#transclusion transclusion} on the same element as `ngApp`.
 *   This includes directives such as {@link ng.ngIf `ngIf`}, {@link ng.ngInclude `ngInclude`} and
 *   {@link ngRoute.ngView `ngView`}.
 *   Doing this misplaces the app {@link ng.$rootElement `$rootElement`} and the app's {@link auto.$injector injector},
 *   causing animations to stop working and making the injector inaccessible from outside the app.
 *
 * You can specify an **AngularJS module** to be used as the root module for the application.  This
 * module will be loaded into the {@link auto.$injector} when the application is bootstrapped. It
 * should contain the application code needed or have dependencies on other modules that will
 * contain the code. See {@link angular.module} for more information.
 *
 * In the example below if the `ngApp` directive were not placed on the `html` element then the
 * document would not be compiled, the `AppController` would not be instantiated and the `{{ a+b }}`
 * would not be resolved to `3`.
 *
 * `ngApp` is the easiest, and most common way to bootstrap an application.
 *
 <example module="ngAppDemo" name="ng-app">
   <file name="index.html">
   <div ng-controller="ngAppDemoController">
     I can add: {{a}} + {{b}} =  {{ a+b }}
   </div>
   </file>
   <file name="script.js">
   angular.module('ngAppDemo', []).controller('ngAppDemoController', function($scope) {
     $scope.a = 1;
     $scope.b = 2;
   });
   </file>
 </example>
 *
 * Using `ngStrictDi`, you would see something like this:
 *
 <example ng-app-included="true" name="strict-di">
   <file name="index.html">
   <div ng-app="ngAppStrictDemo" ng-strict-di>
       <div ng-controller="GoodController1">
           I can add: {{a}} + {{b}} =  {{ a+b }}

           <p>This renders because the controller does not fail to
              instantiate, by using explicit annotation style (see
              script.js for details)
           </p>
       </div>

       <div ng-controller="GoodController2">
           Name: <input ng-model="name"><br />
           Hello, {{name}}!

           <p>This renders because the controller does not fail to
              instantiate, by using explicit annotation style
              (see script.js for details)
           </p>
       </div>

       <div ng-controller="BadController">
           I can add: {{a}} + {{b}} =  {{ a+b }}

           <p>The controller could not be instantiated, due to relying
              on automatic function annotations (which are disabled in
              strict mode). As such, the content of this section is not
              interpolated, and there should be an error in your web console.
           </p>
       </div>
   </div>
   </file>
   <file name="script.js">
   angular.module('ngAppStrictDemo', [])
     // BadController will fail to instantiate, due to relying on automatic function annotation,
     // rather than an explicit annotation
     .controller('BadController', function($scope) {
       $scope.a = 1;
       $scope.b = 2;
     })
     // Unlike BadController, GoodController1 and GoodController2 will not fail to be instantiated,
     // due to using explicit annotations using the array style and $inject property, respectively.
     .controller('GoodController1', ['$scope', function($scope) {
       $scope.a = 1;
       $scope.b = 2;
     }])
     .controller('GoodController2', GoodController2);
     function GoodController2($scope) {
       $scope.name = 'World';
     }
     GoodController2.$inject = ['$scope'];
   </file>
   <file name="style.css">
   div[ng-controller] {
       margin-bottom: 1em;
       -webkit-border-radius: 4px;
       border-radius: 4px;
       border: 1px solid;
       padding: .5em;
   }
   div[ng-controller^=Good] {
       border-color: #d6e9c6;
       background-color: #dff0d8;
       color: #3c763d;
   }
   div[ng-controller^=Bad] {
       border-color: #ebccd1;
       background-color: #f2dede;
       color: #a94442;
       margin-bottom: 0;
   }
   </file>
 </example>
 */
function angularInit(element, bootstrap) {
  var appElement,
      module,
      config = {};

  // The element `element` has priority over any other element.
  forEach(ngAttrPrefixes, function(prefix) {
    var name = prefix + 'app';

    if (!appElement && element.hasAttribute && element.hasAttribute(name)) {
      appElement = element;
      module = element.getAttribute(name);
    }
  });
  forEach(ngAttrPrefixes, function(prefix) {
    var name = prefix + 'app';
    var candidate;

    if (!appElement && (candidate = element.querySelector('[' + name.replace(':', '\\:') + ']'))) {
      appElement = candidate;
      module = candidate.getAttribute(name);
    }
  });
  if (appElement) {
    if (!isAutoBootstrapAllowed) {
      window.console.error('Angular: disabling automatic bootstrap. <script> protocol indicates ' +
          'an extension, document.location.href does not match.');
      return;
    }
    config.strictDi = getNgAttribute(appElement, 'strict-di') !== null;
    bootstrap(appElement, module ? [module] : [], config);
  }
}

/**
 * @ngdoc function
 * @name angular.bootstrap
 * @module ng
 * @description
 * Use this function to manually start up angular application.
 *
 * For more information, see the {@link guide/bootstrap Bootstrap guide}.
 *
 * Angular will detect if it has been loaded into the browser more than once and only allow the
 * first loaded script to be bootstrapped and will report a warning to the browser console for
 * each of the subsequent scripts. This prevents strange results in applications, where otherwise
 * multiple instances of Angular try to work on the DOM.
 *
 * <div class="alert alert-warning">
 * **Note:** Protractor based end-to-end tests cannot use this function to bootstrap manually.
 * They must use {@link ng.directive:ngApp ngApp}.
 * </div>
 *
 * <div class="alert alert-warning">
 * **Note:** Do not bootstrap the app on an element with a directive that uses {@link ng.$compile#transclusion transclusion},
 * such as {@link ng.ngIf `ngIf`}, {@link ng.ngInclude `ngInclude`} and {@link ngRoute.ngView `ngView`}.
 * Doing this misplaces the app {@link ng.$rootElement `$rootElement`} and the app's {@link auto.$injector injector},
 * causing animations to stop working and making the injector inaccessible from outside the app.
 * </div>
 *
 * ```html
 * <!doctype html>
 * <html>
 * <body>
 * <div ng-controller="WelcomeController">
 *   {{greeting}}
 * </div>
 *
 * <script src="angular.js"></script>
 * <script>
 *   var app = angular.module('demo', [])
 *   .controller('WelcomeController', function($scope) {
 *       $scope.greeting = 'Welcome!';
 *   });
 *   angular.bootstrap(document, ['demo']);
 * </script>
 * </body>
 * </html>
 * ```
 *
 * @param {DOMElement} element DOM element which is the root of angular application.
 * @param {Array<String|Function|Array>=} modules an array of modules to load into the application.
 *     Each item in the array should be the name of a predefined module or a (DI annotated)
 *     function that will be invoked by the injector as a `config` block.
 *     See: {@link angular.module modules}
 * @param {Object=} config an object for defining configuration options for the application. The
 *     following keys are supported:
 *
 * * `strictDi` - disable automatic function annotation for the application. This is meant to
 *   assist in finding bugs which break minified code. Defaults to `false`.
 *
 * @returns {auto.$injector} Returns the newly created injector for this app.
 */
function bootstrap(element, modules, config) {
  if (!isObject(config)) config = {};
  var defaultConfig = {
    strictDi: false
  };
  config = extend(defaultConfig, config);
  var doBootstrap = function() {
    element = jqLite(element);

    if (element.injector()) {
      var tag = (element[0] === window.document) ? 'document' : startingTag(element);
      // Encode angle brackets to prevent input from being sanitized to empty string #8683.
      throw ngMinErr(
          'btstrpd',
          'App already bootstrapped with this element \'{0}\'',
          tag.replace(/</,'&lt;').replace(/>/,'&gt;'));
    }

    modules = modules || [];
    modules.unshift(['$provide', function($provide) {
      $provide.value('$rootElement', element);
    }]);

    if (config.debugInfoEnabled) {
      // Pushing so that this overrides `debugInfoEnabled` setting defined in user's `modules`.
      modules.push(['$compileProvider', function($compileProvider) {
        $compileProvider.debugInfoEnabled(true);
      }]);
    }

    modules.unshift('ng');
    var injector = createInjector(modules, config.strictDi);
    injector.invoke(['$rootScope', '$rootElement', '$compile', '$injector',
       function bootstrapApply(scope, element, compile, injector) {
        scope.$apply(function() {
          element.data('$injector', injector);
          compile(element)(scope);
        });
      }]
    );
    return injector;
  };

  var NG_ENABLE_DEBUG_INFO = /^NG_ENABLE_DEBUG_INFO!/;
  var NG_DEFER_BOOTSTRAP = /^NG_DEFER_BOOTSTRAP!/;

  if (window && NG_ENABLE_DEBUG_INFO.test(window.name)) {
    config.debugInfoEnabled = true;
    window.name = window.name.replace(NG_ENABLE_DEBUG_INFO, '');
  }

  if (window && !NG_DEFER_BOOTSTRAP.test(window.name)) {
    return doBootstrap();
  }

  window.name = window.name.replace(NG_DEFER_BOOTSTRAP, '');
  angular.resumeBootstrap = function(extraModules) {
    forEach(extraModules, function(module) {
      modules.push(module);
    });
    return doBootstrap();
  };

  if (isFunction(angular.resumeDeferredBootstrap)) {
    angular.resumeDeferredBootstrap();
  }
}

/**
 * @ngdoc function
 * @name angular.reloadWithDebugInfo
 * @module ng
 * @description
 * Use this function to reload the current application with debug information turned on.
 * This takes precedence over a call to `$compileProvider.debugInfoEnabled(false)`.
 *
 * See {@link ng.$compileProvider#debugInfoEnabled} for more.
 */
function reloadWithDebugInfo() {
  window.name = 'NG_ENABLE_DEBUG_INFO!' + window.name;
  window.location.reload();
}

/**
 * @name angular.getTestability
 * @module ng
 * @description
 * Get the testability service for the instance of Angular on the given
 * element.
 * @param {DOMElement} element DOM element which is the root of angular application.
 */
function getTestability(rootElement) {
  var injector = angular.element(rootElement).injector();
  if (!injector) {
    throw ngMinErr('test',
      'no injector found for element argument to getTestability');
  }
  return injector.get('$$testability');
}

var SNAKE_CASE_REGEXP = /[A-Z]/g;
function snake_case(name, separator) {
  separator = separator || '_';
  return name.replace(SNAKE_CASE_REGEXP, function(letter, pos) {
    return (pos ? separator : '') + letter.toLowerCase();
  });
}

var bindJQueryFired = false;
function bindJQuery() {
  var originalCleanData;

  if (bindJQueryFired) {
    return;
  }

  // bind to jQuery if present;
  var jqName = jq();
  jQuery = isUndefined(jqName) ? window.jQuery :   // use jQuery (if present)
           !jqName             ? undefined     :   // use jqLite
                                 window[jqName];   // use jQuery specified by `ngJq`

  // Use jQuery if it exists with proper functionality, otherwise default to us.
  // Angular 1.2+ requires jQuery 1.7+ for on()/off() support.
  // Angular 1.3+ technically requires at least jQuery 2.1+ but it may work with older
  // versions. It will not work for sure with jQuery <1.7, though.
  if (jQuery && jQuery.fn.on) {
    jqLite = jQuery;
    extend(jQuery.fn, {
      scope: JQLitePrototype.scope,
      isolateScope: JQLitePrototype.isolateScope,
      controller: /** @type {?} */ (JQLitePrototype).controller,
      injector: JQLitePrototype.injector,
      inheritedData: JQLitePrototype.inheritedData
    });

    // All nodes removed from the DOM via various jQuery APIs like .remove()
    // are passed through jQuery.cleanData. Monkey-patch this method to fire
    // the $destroy event on all removed nodes.
    originalCleanData = jQuery.cleanData;
    jQuery.cleanData = function(elems) {
      var events;
      for (var i = 0, elem; (elem = elems[i]) != null; i++) {
        events = jQuery._data(elem, 'events');
        if (events && events.$destroy) {
          jQuery(elem).triggerHandler('$destroy');
        }
      }
      originalCleanData(elems);
    };
  } else {
    jqLite = JQLite;
  }

  angular.element = jqLite;

  // Prevent double-proxying.
  bindJQueryFired = true;
}

/**
 * throw error if the argument is falsy.
 */
function assertArg(arg, name, reason) {
  if (!arg) {
    throw ngMinErr('areq', 'Argument \'{0}\' is {1}', (name || '?'), (reason || 'required'));
  }
  return arg;
}

function assertArgFn(arg, name, acceptArrayAnnotation) {
  if (acceptArrayAnnotation && isArray(arg)) {
      arg = arg[arg.length - 1];
  }

  assertArg(isFunction(arg), name, 'not a function, got ' +
      (arg && typeof arg === 'object' ? arg.constructor.name || 'Object' : typeof arg));
  return arg;
}

/**
 * throw error if the name given is hasOwnProperty
 * @param  {String} name    the name to test
 * @param  {String} context the context in which the name is used, such as module or directive
 */
function assertNotHasOwnProperty(name, context) {
  if (name === 'hasOwnProperty') {
    throw ngMinErr('badname', 'hasOwnProperty is not a valid {0} name', context);
  }
}

/**
 * Return the value accessible from the object by path. Any undefined traversals are ignored
 * @param {Object} obj starting object
 * @param {String} path path to traverse
 * @param {boolean} [bindFnToScope=true]
 * @returns {Object} value as accessible by path
 */
//TODO(misko): this function needs to be removed
function getter(obj, path, bindFnToScope) {
  if (!path) return obj;
  var keys = path.split('.');
  var key;
  var lastInstance = obj;
  var len = keys.length;

  for (var i = 0; i < len; i++) {
    key = keys[i];
    if (obj) {
      obj = (lastInstance = obj)[key];
    }
  }
  if (!bindFnToScope && isFunction(obj)) {
    return bind(lastInstance, obj);
  }
  return obj;
}

/**
 * Return the DOM siblings between the first and last node in the given array.
 * @param {Array} array like object
 * @returns {Array} the inputted object or a jqLite collection containing the nodes
 */
function getBlockNodes(nodes) {
  // TODO(perf): update `nodes` instead of creating a new object?
  var node = nodes[0];
  var endNode = nodes[nodes.length - 1];
  var blockNodes;

  for (var i = 1; node !== endNode && (node = node.nextSibling); i++) {
    if (blockNodes || nodes[i] !== node) {
      if (!blockNodes) {
        blockNodes = jqLite(slice.call(nodes, 0, i));
      }
      blockNodes.push(node);
    }
  }

  return blockNodes || nodes;
}


/**
 * Creates a new object without a prototype. This object is useful for lookup without having to
 * guard against prototypically inherited properties via hasOwnProperty.
 *
 * Related micro-benchmarks:
 * - http://jsperf.com/object-create2
 * - http://jsperf.com/proto-map-lookup/2
 * - http://jsperf.com/for-in-vs-object-keys2
 *
 * @returns {Object}
 */
function createMap() {
  return Object.create(null);
}

function stringify(value) {
  if (value == null) { // null || undefined
    return '';
  }
  switch (typeof value) {
    case 'string':
      break;
    case 'number':
      value = '' + value;
      break;
    default:
      if (hasCustomToString(value) && !isArray(value) && !isDate(value)) {
        value = value.toString();
      } else {
        value = toJson(value);
      }
  }

  return value;
}

var NODE_TYPE_ELEMENT = 1;
var NODE_TYPE_ATTRIBUTE = 2;
var NODE_TYPE_TEXT = 3;
var NODE_TYPE_COMMENT = 8;
var NODE_TYPE_DOCUMENT = 9;
var NODE_TYPE_DOCUMENT_FRAGMENT = 11;

/**
 * @ngdoc type
 * @name angular.Module
 * @module ng
 * @description
 *
 * Interface for configuring angular {@link angular.module modules}.
 */

function setupModuleLoader(window) {

  var $injectorMinErr = minErr('$injector');
  var ngMinErr = minErr('ng');

  function ensure(obj, name, factory) {
    return obj[name] || (obj[name] = factory());
  }

  var angular = ensure(window, 'angular', Object);

  // We need to expose `angular.$$minErr` to modules such as `ngResource` that reference it during bootstrap
  angular.$$minErr = angular.$$minErr || minErr;

  return ensure(angular, 'module', function() {
    /** @type {Object.<string, angular.Module>} */
    var modules = {};

    /**
     * @ngdoc function
     * @name angular.module
     * @module ng
     * @description
     *
     * The `angular.module` is a global place for creating, registering and retrieving Angular
     * modules.
     * All modules (angular core or 3rd party) that should be available to an application must be
     * registered using this mechanism.
     *
     * Passing one argument retrieves an existing {@link angular.Module},
     * whereas passing more than one argument creates a new {@link angular.Module}
     *
     *
     * # Module
     *
     * A module is a collection of services, directives, controllers, filters, and configuration information.
     * `angular.module` is used to configure the {@link auto.$injector $injector}.
     *
     * ```js
     * // Create a new module
     * var myModule = angular.module('myModule', []);
     *
     * // register a new service
     * myModule.value('appName', 'MyCoolApp');
     *
     * // configure existing services inside initialization blocks.
     * myModule.config(['$locationProvider', function($locationProvider) {
     *   // Configure existing providers
     *   $locationProvider.hashPrefix('!');
     * }]);
     * ```
     *
     * Then you can create an injector and load your modules like this:
     *
     * ```js
     * var injector = angular.injector(['ng', 'myModule'])
     * ```
     *
     * However it's more likely that you'll just use
     * {@link ng.directive:ngApp ngApp} or
     * {@link angular.bootstrap} to simplify this process for you.
     *
     * @param {!string} name The name of the module to create or retrieve.
     * @param {!Array.<string>=} requires If specified then new module is being created. If
     *        unspecified then the module is being retrieved for further configuration.
     * @param {Function=} configFn Optional configuration function for the module. Same as
     *        {@link angular.Module#config Module#config()}.
     * @returns {angular.Module} new module with the {@link angular.Module} api.
     */
    return function module(name, requires, configFn) {

      var info = {};

      var assertNotHasOwnProperty = function(name, context) {
        if (name === 'hasOwnProperty') {
          throw ngMinErr('badname', 'hasOwnProperty is not a valid {0} name', context);
        }
      };

      assertNotHasOwnProperty(name, 'module');
      if (requires && modules.hasOwnProperty(name)) {
        modules[name] = null;
      }
      return ensure(modules, name, function() {
        if (!requires) {
          throw $injectorMinErr('nomod', 'Module \'{0}\' is not available! You either misspelled ' +
             'the module name or forgot to load it. If registering a module ensure that you ' +
             'specify the dependencies as the second argument.', name);
        }

        /** @type {!Array.<Array.<*>>} */
        var invokeQueue = [];

        /** @type {!Array.<Function>} */
        var configBlocks = [];

        /** @type {!Array.<Function>} */
        var runBlocks = [];

        var config = invokeLater('$injector', 'invoke', 'push', configBlocks);

        /** @type {angular.Module} */
        var moduleInstance = {
          // Private state
          _invokeQueue: invokeQueue,
          _configBlocks: configBlocks,
          _runBlocks: runBlocks,

          /**
           * @ngdoc method
           * @name angular.Module#info
           * @module ng
           *
           * @param {Object=} info Information about the module
           * @returns {Object|Module} The current info object for this module if called as a getter,
           *                          or `this` if called as a setter.
           *
           * @description
           * Read and write custom information about this module.
           * For example you could put the version of the module in here.
           *
           * ```js
           * angular.module('myModule', []).info({ version: '1.0.0' });
           * ```
           *
           * The version could then be read back out by accessing the module elsewhere:
           *
           * ```
           * var version = angular.module('myModule').info().version;
           * ```
           *
           * You can also retrieve this information during runtime via the
           * {@link $injector#modules `$injector.modules`} property:
           *
           * ```js
           * var version = $injector.modules['myModule'].info().version;
           * ```
           */
          info: function(value) {
            if (isDefined(value)) {
              if (!isObject(value)) throw ngMinErr('aobj', 'Argument \'{0}\' must be an object', 'value');
              info = value;
              return this;
            }
            return info;
          },

          /**
           * @ngdoc property
           * @name angular.Module#requires
           * @module ng
           *
           * @description
           * Holds the list of modules which the injector will load before the current module is
           * loaded.
           */
          requires: requires,

          /**
           * @ngdoc property
           * @name angular.Module#name
           * @module ng
           *
           * @description
           * Name of the module.
           */
          name: name,


          /**
           * @ngdoc method
           * @name angular.Module#provider
           * @module ng
           * @param {string} name service name
           * @param {Function} providerType Construction function for creating new instance of the
           *                                service.
           * @description
           * See {@link auto.$provide#provider $provide.provider()}.
           */
          provider: invokeLaterAndSetModuleName('$provide', 'provider'),

          /**
           * @ngdoc method
           * @name angular.Module#factory
           * @module ng
           * @param {string} name service name
           * @param {Function} providerFunction Function for creating new instance of the service.
           * @description
           * See {@link auto.$provide#factory $provide.factory()}.
           */
          factory: invokeLaterAndSetModuleName('$provide', 'factory'),

          /**
           * @ngdoc method
           * @name angular.Module#service
           * @module ng
           * @param {string} name service name
           * @param {Function} constructor A constructor function that will be instantiated.
           * @description
           * See {@link auto.$provide#service $provide.service()}.
           */
          service: invokeLaterAndSetModuleName('$provide', 'service'),

          /**
           * @ngdoc method
           * @name angular.Module#value
           * @module ng
           * @param {string} name service name
           * @param {*} object Service instance object.
           * @description
           * See {@link auto.$provide#value $provide.value()}.
           */
          value: invokeLater('$provide', 'value'),

          /**
           * @ngdoc method
           * @name angular.Module#constant
           * @module ng
           * @param {string} name constant name
           * @param {*} object Constant value.
           * @description
           * Because the constants are fixed, they get applied before other provide methods.
           * See {@link auto.$provide#constant $provide.constant()}.
           */
          constant: invokeLater('$provide', 'constant', 'unshift'),

           /**
           * @ngdoc method
           * @name angular.Module#decorator
           * @module ng
           * @param {string} name The name of the service to decorate.
           * @param {Function} decorFn This function will be invoked when the service needs to be
           *                           instantiated and should return the decorated service instance.
           * @description
           * See {@link auto.$provide#decorator $provide.decorator()}.
           */
          decorator: invokeLaterAndSetModuleName('$provide', 'decorator', configBlocks),

          /**
           * @ngdoc method
           * @name angular.Module#animation
           * @module ng
           * @param {string} name animation name
           * @param {Function} animationFactory Factory function for creating new instance of an
           *                                    animation.
           * @description
           *
           * **NOTE**: animations take effect only if the **ngAnimate** module is loaded.
           *
           *
           * Defines an animation hook that can be later used with
           * {@link $animate $animate} service and directives that use this service.
           *
           * ```js
           * module.animation('.animation-name', function($inject1, $inject2) {
           *   return {
           *     eventName : function(element, done) {
           *       //code to run the animation
           *       //once complete, then run done()
           *       return function cancellationFunction(element) {
           *         //code to cancel the animation
           *       }
           *     }
           *   }
           * })
           * ```
           *
           * See {@link ng.$animateProvider#register $animateProvider.register()} and
           * {@link ngAnimate ngAnimate module} for more information.
           */
          animation: invokeLaterAndSetModuleName('$animateProvider', 'register'),

          /**
           * @ngdoc method
           * @name angular.Module#filter
           * @module ng
           * @param {string} name Filter name - this must be a valid angular expression identifier
           * @param {Function} filterFactory Factory function for creating new instance of filter.
           * @description
           * See {@link ng.$filterProvider#register $filterProvider.register()}.
           *
           * <div class="alert alert-warning">
           * **Note:** Filter names must be valid angular {@link expression} identifiers, such as `uppercase` or `orderBy`.
           * Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
           * your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
           * (`myapp_subsection_filterx`).
           * </div>
           */
          filter: invokeLaterAndSetModuleName('$filterProvider', 'register'),

          /**
           * @ngdoc method
           * @name angular.Module#controller
           * @module ng
           * @param {string|Object} name Controller name, or an object map of controllers where the
           *    keys are the names and the values are the constructors.
           * @param {Function} constructor Controller constructor function.
           * @description
           * See {@link ng.$controllerProvider#register $controllerProvider.register()}.
           */
          controller: invokeLaterAndSetModuleName('$controllerProvider', 'register'),

          /**
           * @ngdoc method
           * @name angular.Module#directive
           * @module ng
           * @param {string|Object} name Directive name, or an object map of directives where the
           *    keys are the names and the values are the factories.
           * @param {Function} directiveFactory Factory function for creating new instance of
           * directives.
           * @description
           * See {@link ng.$compileProvider#directive $compileProvider.directive()}.
           */
          directive: invokeLaterAndSetModuleName('$compileProvider', 'directive'),

          /**
           * @ngdoc method
           * @name angular.Module#component
           * @module ng
           * @param {string} name Name of the component in camel-case (i.e. myComp which will match as my-comp)
           * @param {Object} options Component definition object (a simplified
           *    {@link ng.$compile#directive-definition-object directive definition object})
           *
           * @description
           * See {@link ng.$compileProvider#component $compileProvider.component()}.
           */
          component: invokeLaterAndSetModuleName('$compileProvider', 'component'),

          /**
           * @ngdoc method
           * @name angular.Module#config
           * @module ng
           * @param {Function} configFn Execute this function on module load. Useful for service
           *    configuration.
           * @description
           * Use this method to register work which needs to be performed on module loading.
           * For more about how to configure services, see
           * {@link providers#provider-recipe Provider Recipe}.
           */
          config: config,

          /**
           * @ngdoc method
           * @name angular.Module#run
           * @module ng
           * @param {Function} initializationFn Execute this function after injector creation.
           *    Useful for application initialization.
           * @description
           * Use this method to register work which should be performed when the injector is done
           * loading all modules.
           */
          run: function(block) {
            runBlocks.push(block);
            return this;
          }
        };

        if (configFn) {
          config(configFn);
        }

        return moduleInstance;

        /**
         * @param {string} provider
         * @param {string} method
         * @param {String=} insertMethod
         * @returns {angular.Module}
         */
        function invokeLater(provider, method, insertMethod, queue) {
          if (!queue) queue = invokeQueue;
          return function() {
            queue[insertMethod || 'push']([provider, method, arguments]);
            return moduleInstance;
          };
        }

        /**
         * @param {string} provider
         * @param {string} method
         * @returns {angular.Module}
         */
        function invokeLaterAndSetModuleName(provider, method, queue) {
          if (!queue) queue = invokeQueue;
          return function(recipeName, factoryFunction) {
            if (factoryFunction && isFunction(factoryFunction)) factoryFunction.$$moduleName = name;
            queue.push([provider, method, arguments]);
            return moduleInstance;
          };
        }
      });
    };
  });

}

/* global shallowCopy: true */

/**
 * Creates a shallow copy of an object, an array or a primitive.
 *
 * Assumes that there are no proto properties for objects.
 */
function shallowCopy(src, dst) {
  if (isArray(src)) {
    dst = dst || [];

    for (var i = 0, ii = src.length; i < ii; i++) {
      dst[i] = src[i];
    }
  } else if (isObject(src)) {
    dst = dst || {};

    for (var key in src) {
      if (!(key.charAt(0) === '$' && key.charAt(1) === '$')) {
        dst[key] = src[key];
      }
    }
  }

  return dst || src;
}

/* global toDebugString: true */

function serializeObject(obj, maxDepth) {
  var seen = [];

  // There is no direct way to stringify object until reaching a specific depth
  // and a very deep object can cause a performance issue, so we copy the object
  // based on this specific depth and then stringify it.
  if (isValidObjectMaxDepth(maxDepth)) {
    obj = copy(obj, null, maxDepth);
  }
  return JSON.stringify(obj, function(key, val) {
    val = toJsonReplacer(key, val);
    if (isObject(val)) {

      if (seen.indexOf(val) >= 0) return '...';

      seen.push(val);
    }
    return val;
  });
}

function toDebugString(obj, maxDepth) {
  if (typeof obj === 'function') {
    return obj.toString().replace(/ \{[\s\S]*$/, '');
  } else if (isUndefined(obj)) {
    return 'undefined';
  } else if (typeof obj !== 'string') {
    return serializeObject(obj, maxDepth);
  }
  return obj;
}

/* global angularModule: true,
  version: true,

  $CompileProvider,

  htmlAnchorDirective,
  inputDirective,
  inputDirective,
  formDirective,
  scriptDirective,
  selectDirective,
  optionDirective,
  ngBindDirective,
  ngBindHtmlDirective,
  ngBindTemplateDirective,
  ngClassDirective,
  ngClassEvenDirective,
  ngClassOddDirective,
  ngCloakDirective,
  ngControllerDirective,
  ngFormDirective,
  ngHideDirective,
  ngIfDirective,
  ngIncludeDirective,
  ngIncludeFillContentDirective,
  ngInitDirective,
  ngNonBindableDirective,
  ngPluralizeDirective,
  ngRepeatDirective,
  ngShowDirective,
  ngStyleDirective,
  ngSwitchDirective,
  ngSwitchWhenDirective,
  ngSwitchDefaultDirective,
  ngOptionsDirective,
  ngTranscludeDirective,
  ngModelDirective,
  ngListDirective,
  ngChangeDirective,
  patternDirective,
  patternDirective,
  requiredDirective,
  requiredDirective,
  minlengthDirective,
  minlengthDirective,
  maxlengthDirective,
  maxlengthDirective,
  ngValueDirective,
  ngModelOptionsDirective,
  ngAttributeAliasDirectives,
  ngEventDirectives,

  $AnchorScrollProvider,
  $AnimateProvider,
  $CoreAnimateCssProvider,
  $$CoreAnimateJsProvider,
  $$CoreAnimateQueueProvider,
  $$AnimateRunnerFactoryProvider,
  $$AnimateAsyncRunFactoryProvider,
  $BrowserProvider,
  $CacheFactoryProvider,
  $ControllerProvider,
  $DateProvider,
  $DocumentProvider,
  $$IsDocumentHiddenProvider,
  $ExceptionHandlerProvider,
  $FilterProvider,
  $$ForceReflowProvider,
  $InterpolateProvider,
  $IntervalProvider,
  $HttpProvider,
  $HttpParamSerializerProvider,
  $HttpParamSerializerJQLikeProvider,
  $HttpBackendProvider,
  $xhrFactoryProvider,
  $jsonpCallbacksProvider,
  $LocationProvider,
  $LogProvider,
  $$MapProvider,
  $ParseProvider,
  $RootScopeProvider,
  $QProvider,
  $$QProvider,
  $$SanitizeUriProvider,
  $SceProvider,
  $SceDelegateProvider,
  $SnifferProvider,
  $TemplateCacheProvider,
  $TemplateRequestProvider,
  $$TestabilityProvider,
  $TimeoutProvider,
  $$RAFProvider,
  $WindowProvider,
  $$jqLiteProvider,
  $$CookieReaderProvider
*/


/**
 * @ngdoc object
 * @name angular.version
 * @module ng
 * @description
 * An object that contains information about the current AngularJS version.
 *
 * This object has the following properties:
 *
 * - `full` – `{string}` – Full version string, such as "0.9.18".
 * - `major` – `{number}` – Major version number, such as "0".
 * - `minor` – `{number}` – Minor version number, such as "9".
 * - `dot` – `{number}` – Dot version number, such as "18".
 * - `codeName` – `{string}` – Code name of the release, such as "jiggling-armfat".
 */
var version = {
  // These placeholder strings will be replaced by grunt's `build` task.
  // They need to be double- or single-quoted.
  full: '1.6.3',
  major: 1,
  minor: 6,
  dot: 3,
  codeName: 'scriptalicious-bootstrapping'
};


function publishExternalAPI(angular) {
  extend(angular, {
    'errorHandlingConfig': errorHandlingConfig,
    'bootstrap': bootstrap,
    'copy': copy,
    'extend': extend,
    'merge': merge,
    'equals': equals,
    'element': jqLite,
    'forEach': forEach,
    'injector': createInjector,
    'noop': noop,
    'bind': bind,
    'toJson': toJson,
    'fromJson': fromJson,
    'identity': identity,
    'isUndefined': isUndefined,
    'isDefined': isDefined,
    'isString': isString,
    'isFunction': isFunction,
    'isObject': isObject,
    'isNumber': isNumber,
    'isElement': isElement,
    'isArray': isArray,
    'version': version,
    'isDate': isDate,
    'lowercase': lowercase,
    'uppercase': uppercase,
    'callbacks': {$$counter: 0},
    'getTestability': getTestability,
    'reloadWithDebugInfo': reloadWithDebugInfo,
    '$$minErr': minErr,
    '$$csp': csp,
    '$$encodeUriSegment': encodeUriSegment,
    '$$encodeUriQuery': encodeUriQuery,
    '$$stringify': stringify
  });

  angularModule = setupModuleLoader(window);

  angularModule('ng', ['ngLocale'], ['$provide',
    function ngModule($provide) {
      // $$sanitizeUriProvider needs to be before $compileProvider as it is used by it.
      $provide.provider({
        $$sanitizeUri: $$SanitizeUriProvider
      });
      $provide.provider('$compile', $CompileProvider).
        directive({
            a: htmlAnchorDirective,
            input: inputDirective,
            textarea: inputDirective,
            form: formDirective,
            script: scriptDirective,
            select: selectDirective,
            option: optionDirective,
            ngBind: ngBindDirective,
            ngBindHtml: ngBindHtmlDirective,
            ngBindTemplate: ngBindTemplateDirective,
            ngClass: ngClassDirective,
            ngClassEven: ngClassEvenDirective,
            ngClassOdd: ngClassOddDirective,
            ngCloak: ngCloakDirective,
            ngController: ngControllerDirective,
            ngForm: ngFormDirective,
            ngHide: ngHideDirective,
            ngIf: ngIfDirective,
            ngInclude: ngIncludeDirective,
            ngInit: ngInitDirective,
            ngNonBindable: ngNonBindableDirective,
            ngPluralize: ngPluralizeDirective,
            ngRepeat: ngRepeatDirective,
            ngShow: ngShowDirective,
            ngStyle: ngStyleDirective,
            ngSwitch: ngSwitchDirective,
            ngSwitchWhen: ngSwitchWhenDirective,
            ngSwitchDefault: ngSwitchDefaultDirective,
            ngOptions: ngOptionsDirective,
            ngTransclude: ngTranscludeDirective,
            ngModel: ngModelDirective,
            ngList: ngListDirective,
            ngChange: ngChangeDirective,
            pattern: patternDirective,
            ngPattern: patternDirective,
            required: requiredDirective,
            ngRequired: requiredDirective,
            minlength: minlengthDirective,
            ngMinlength: minlengthDirective,
            maxlength: maxlengthDirective,
            ngMaxlength: maxlengthDirective,
            ngValue: ngValueDirective,
            ngModelOptions: ngModelOptionsDirective
        }).
        directive({
          ngInclude: ngIncludeFillContentDirective
        }).
        directive(ngAttributeAliasDirectives).
        directive(ngEventDirectives);
      $provide.provider({
        $anchorScroll: $AnchorScrollProvider,
        $animate: $AnimateProvider,
        $animateCss: $CoreAnimateCssProvider,
        $$animateJs: $$CoreAnimateJsProvider,
        $$animateQueue: $$CoreAnimateQueueProvider,
        $$AnimateRunner: $$AnimateRunnerFactoryProvider,
        $$animateAsyncRun: $$AnimateAsyncRunFactoryProvider,
        $browser: $BrowserProvider,
        $cacheFactory: $CacheFactoryProvider,
        $controller: $ControllerProvider,
        $document: $DocumentProvider,
        $$isDocumentHidden: $$IsDocumentHiddenProvider,
        $exceptionHandler: $ExceptionHandlerProvider,
        $filter: $FilterProvider,
        $$forceReflow: $$ForceReflowProvider,
        $interpolate: $InterpolateProvider,
        $interval: $IntervalProvider,
        $http: $HttpProvider,
        $httpParamSerializer: $HttpParamSerializerProvider,
        $httpParamSerializerJQLike: $HttpParamSerializerJQLikeProvider,
        $httpBackend: $HttpBackendProvider,
        $xhrFactory: $xhrFactoryProvider,
        $jsonpCallbacks: $jsonpCallbacksProvider,
        $location: $LocationProvider,
        $log: $LogProvider,
        $parse: $ParseProvider,
        $rootScope: $RootScopeProvider,
        $q: $QProvider,
        $$q: $$QProvider,
        $sce: $SceProvider,
        $sceDelegate: $SceDelegateProvider,
        $sniffer: $SnifferProvider,
        $templateCache: $TemplateCacheProvider,
        $templateRequest: $TemplateRequestProvider,
        $$testability: $$TestabilityProvider,
        $timeout: $TimeoutProvider,
        $window: $WindowProvider,
        $$rAF: $$RAFProvider,
        $$jqLite: $$jqLiteProvider,
        $$Map: $$MapProvider,
        $$cookieReader: $$CookieReaderProvider
      });
    }
  ])
  .info({ angularVersion: '1.6.3' });
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/* global
  JQLitePrototype: true,
  BOOLEAN_ATTR: true,
  ALIASED_ATTR: true
*/

//////////////////////////////////
//JQLite
//////////////////////////////////

/**
 * @ngdoc function
 * @name angular.element
 * @module ng
 * @kind function
 *
 * @description
 * Wraps a raw DOM element or HTML string as a [jQuery](http://jquery.com) element.
 *
 * If jQuery is available, `angular.element` is an alias for the
 * [jQuery](http://api.jquery.com/jQuery/) function. If jQuery is not available, `angular.element`
 * delegates to Angular's built-in subset of jQuery, called "jQuery lite" or **jqLite**.
 *
 * jqLite is a tiny, API-compatible subset of jQuery that allows
 * Angular to manipulate the DOM in a cross-browser compatible way. jqLite implements only the most
 * commonly needed functionality with the goal of having a very small footprint.
 *
 * To use `jQuery`, simply ensure it is loaded before the `angular.js` file. You can also use the
 * {@link ngJq `ngJq`} directive to specify that jqlite should be used over jQuery, or to use a
 * specific version of jQuery if multiple versions exist on the page.
 *
 * <div class="alert alert-info">**Note:** All element references in Angular are always wrapped with jQuery or
 * jqLite (such as the element argument in a directive's compile / link function). They are never raw DOM references.</div>
 *
 * <div class="alert alert-warning">**Note:** Keep in mind that this function will not find elements
 * by tag name / CSS selector. For lookups by tag name, try instead `angular.element(document).find(...)`
 * or `$document.find()`, or use the standard DOM APIs, e.g. `document.querySelectorAll()`.</div>
 *
 * ## Angular's jqLite
 * jqLite provides only the following jQuery methods:
 *
 * - [`addClass()`](http://api.jquery.com/addClass/) - Does not support a function as first argument
 * - [`after()`](http://api.jquery.com/after/)
 * - [`append()`](http://api.jquery.com/append/)
 * - [`attr()`](http://api.jquery.com/attr/) - Does not support functions as parameters
 * - [`bind()`](http://api.jquery.com/bind/) (_deprecated_, use [`on()`](http://api.jquery.com/on/)) - Does not support namespaces, selectors or eventData
 * - [`children()`](http://api.jquery.com/children/) - Does not support selectors
 * - [`clone()`](http://api.jquery.com/clone/)
 * - [`contents()`](http://api.jquery.com/contents/)
 * - [`css()`](http://api.jquery.com/css/) - Only retrieves inline-styles, does not call `getComputedStyle()`.
 *   As a setter, does not convert numbers to strings or append 'px', and also does not have automatic property prefixing.
 * - [`data()`](http://api.jquery.com/data/)
 * - [`detach()`](http://api.jquery.com/detach/)
 * - [`empty()`](http://api.jquery.com/empty/)
 * - [`eq()`](http://api.jquery.com/eq/)
 * - [`find()`](http://api.jquery.com/find/) - Limited to lookups by tag name
 * - [`hasClass()`](http://api.jquery.com/hasClass/)
 * - [`html()`](http://api.jquery.com/html/)
 * - [`next()`](http://api.jquery.com/next/) - Does not support selectors
 * - [`on()`](http://api.jquery.com/on/) - Does not support namespaces, selectors or eventData
 * - [`off()`](http://api.jquery.com/off/) - Does not support namespaces, selectors or event object as parameter
 * - [`one()`](http://api.jquery.com/one/) - Does not support namespaces or selectors
 * - [`parent()`](http://api.jquery.com/parent/) - Does not support selectors
 * - [`prepend()`](http://api.jquery.com/prepend/)
 * - [`prop()`](http://api.jquery.com/prop/)
 * - [`ready()`](http://api.jquery.com/ready/) (_deprecated_, use `angular.element(callback)` instead of `angular.element(document).ready(callback)`)
 * - [`remove()`](http://api.jquery.com/remove/)
 * - [`removeAttr()`](http://api.jquery.com/removeAttr/) - Does not support multiple attributes
 * - [`removeClass()`](http://api.jquery.com/removeClass/) - Does not support a function as first argument
 * - [`removeData()`](http://api.jquery.com/removeData/)
 * - [`replaceWith()`](http://api.jquery.com/replaceWith/)
 * - [`text()`](http://api.jquery.com/text/)
 * - [`toggleClass()`](http://api.jquery.com/toggleClass/) - Does not support a function as first argument
 * - [`triggerHandler()`](http://api.jquery.com/triggerHandler/) - Passes a dummy event object to handlers
 * - [`unbind()`](http://api.jquery.com/unbind/) (_deprecated_, use [`off()`](http://api.jquery.com/off/)) - Does not support namespaces or event object as parameter
 * - [`val()`](http://api.jquery.com/val/)
 * - [`wrap()`](http://api.jquery.com/wrap/)
 *
 * ## jQuery/jqLite Extras
 * Angular also provides the following additional methods and events to both jQuery and jqLite:
 *
 * ### Events
 * - `$destroy` - AngularJS intercepts all jqLite/jQuery's DOM destruction apis and fires this event
 *    on all DOM nodes being removed.  This can be used to clean up any 3rd party bindings to the DOM
 *    element before it is removed.
 *
 * ### Methods
 * - `controller(name)` - retrieves the controller of the current element or its parent. By default
 *   retrieves controller associated with the `ngController` directive. If `name` is provided as
 *   camelCase directive name, then the controller for this directive will be retrieved (e.g.
 *   `'ngModel'`).
 * - `injector()` - retrieves the injector of the current element or its parent.
 * - `scope()` - retrieves the {@link ng.$rootScope.Scope scope} of the current
 *   element or its parent. Requires {@link guide/production#disabling-debug-data Debug Data} to
 *   be enabled.
 * - `isolateScope()` - retrieves an isolate {@link ng.$rootScope.Scope scope} if one is attached directly to the
 *   current element. This getter should be used only on elements that contain a directive which starts a new isolate
 *   scope. Calling `scope()` on this element always returns the original non-isolate scope.
 *   Requires {@link guide/production#disabling-debug-data Debug Data} to be enabled.
 * - `inheritedData()` - same as `data()`, but walks up the DOM until a value is found or the top
 *   parent element is reached.
 *
 * @knownIssue You cannot spy on `angular.element` if you are using Jasmine version 1.x. See
 * https://github.com/angular/angular.js/issues/14251 for more information.
 *
 * @param {string|DOMElement} element HTML string or DOMElement to be wrapped into jQuery.
 * @returns {Object} jQuery object.
 */

JQLite.expando = 'ng339';

var jqCache = JQLite.cache = {},
    jqId = 1;

/*
 * !!! This is an undocumented "private" function !!!
 */
JQLite._data = function(node) {
  //jQuery always returns an object on cache miss
  return this.cache[node[this.expando]] || {};
};

function jqNextId() { return ++jqId; }


var DASH_LOWERCASE_REGEXP = /-([a-z])/g;
var MS_HACK_REGEXP = /^-ms-/;
var MOUSE_EVENT_MAP = { mouseleave: 'mouseout', mouseenter: 'mouseover' };
var jqLiteMinErr = minErr('jqLite');

/**
 * Converts kebab-case to camelCase.
 * There is also a special case for the ms prefix starting with a lowercase letter.
 * @param name Name to normalize
 */
function cssKebabToCamel(name) {
    return kebabToCamel(name.replace(MS_HACK_REGEXP, 'ms-'));
}

function fnCamelCaseReplace(all, letter) {
  return letter.toUpperCase();
}

/**
 * Converts kebab-case to camelCase.
 * @param name Name to normalize
 */
function kebabToCamel(name) {
  return name
    .replace(DASH_LOWERCASE_REGEXP, fnCamelCaseReplace);
}

var SINGLE_TAG_REGEXP = /^<([\w-]+)\s*\/?>(?:<\/\1>|)$/;
var HTML_REGEXP = /<|&#?\w+;/;
var TAG_NAME_REGEXP = /<([\w:-]+)/;
var XHTML_TAG_REGEXP = /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([\w:-]+)[^>]*)\/>/gi;

var wrapMap = {
  'option': [1, '<select multiple="multiple">', '</select>'],

  'thead': [1, '<table>', '</table>'],
  'col': [2, '<table><colgroup>', '</colgroup></table>'],
  'tr': [2, '<table><tbody>', '</tbody></table>'],
  'td': [3, '<table><tbody><tr>', '</tr></tbody></table>'],
  '_default': [0, '', '']
};

wrapMap.optgroup = wrapMap.option;
wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
wrapMap.th = wrapMap.td;


function jqLiteIsTextNode(html) {
  return !HTML_REGEXP.test(html);
}

function jqLiteAcceptsData(node) {
  // The window object can accept data but has no nodeType
  // Otherwise we are only interested in elements (1) and documents (9)
  var nodeType = node.nodeType;
  return nodeType === NODE_TYPE_ELEMENT || !nodeType || nodeType === NODE_TYPE_DOCUMENT;
}

function jqLiteHasData(node) {
  for (var key in jqCache[node.ng339]) {
    return true;
  }
  return false;
}

function jqLiteCleanData(nodes) {
  for (var i = 0, ii = nodes.length; i < ii; i++) {
    jqLiteRemoveData(nodes[i]);
  }
}

function jqLiteBuildFragment(html, context) {
  var tmp, tag, wrap,
      fragment = context.createDocumentFragment(),
      nodes = [], i;

  if (jqLiteIsTextNode(html)) {
    // Convert non-html into a text node
    nodes.push(context.createTextNode(html));
  } else {
    // Convert html into DOM nodes
    tmp = fragment.appendChild(context.createElement('div'));
    tag = (TAG_NAME_REGEXP.exec(html) || ['', ''])[1].toLowerCase();
    wrap = wrapMap[tag] || wrapMap._default;
    tmp.innerHTML = wrap[1] + html.replace(XHTML_TAG_REGEXP, '<$1></$2>') + wrap[2];

    // Descend through wrappers to the right content
    i = wrap[0];
    while (i--) {
      tmp = tmp.lastChild;
    }

    nodes = concat(nodes, tmp.childNodes);

    tmp = fragment.firstChild;
    tmp.textContent = '';
  }

  // Remove wrapper from fragment
  fragment.textContent = '';
  fragment.innerHTML = ''; // Clear inner HTML
  forEach(nodes, function(node) {
    fragment.appendChild(node);
  });

  return fragment;
}

function jqLiteParseHTML(html, context) {
  context = context || window.document;
  var parsed;

  if ((parsed = SINGLE_TAG_REGEXP.exec(html))) {
    return [context.createElement(parsed[1])];
  }

  if ((parsed = jqLiteBuildFragment(html, context))) {
    return parsed.childNodes;
  }

  return [];
}

function jqLiteWrapNode(node, wrapper) {
  var parent = node.parentNode;

  if (parent) {
    parent.replaceChild(wrapper, node);
  }

  wrapper.appendChild(node);
}


// IE9-11 has no method "contains" in SVG element and in Node.prototype. Bug #10259.
var jqLiteContains = window.Node.prototype.contains || /** @this */ function(arg) {
  // eslint-disable-next-line no-bitwise
  return !!(this.compareDocumentPosition(arg) & 16);
};

/////////////////////////////////////////////
function JQLite(element) {
  if (element instanceof JQLite) {
    return element;
  }

  var argIsString;

  if (isString(element)) {
    element = trim(element);
    argIsString = true;
  }
  if (!(this instanceof JQLite)) {
    if (argIsString && element.charAt(0) !== '<') {
      throw jqLiteMinErr('nosel', 'Looking up elements via selectors is not supported by jqLite! See: http://docs.angularjs.org/api/angular.element');
    }
    return new JQLite(element);
  }

  if (argIsString) {
    jqLiteAddNodes(this, jqLiteParseHTML(element));
  } else if (isFunction(element)) {
    jqLiteReady(element);
  } else {
    jqLiteAddNodes(this, element);
  }
}

function jqLiteClone(element) {
  return element.cloneNode(true);
}

function jqLiteDealoc(element, onlyDescendants) {
  if (!onlyDescendants) jqLiteRemoveData(element);

  if (element.querySelectorAll) {
    var descendants = element.querySelectorAll('*');
    for (var i = 0, l = descendants.length; i < l; i++) {
      jqLiteRemoveData(descendants[i]);
    }
  }
}

function jqLiteOff(element, type, fn, unsupported) {
  if (isDefined(unsupported)) throw jqLiteMinErr('offargs', 'jqLite#off() does not support the `selector` argument');

  var expandoStore = jqLiteExpandoStore(element);
  var events = expandoStore && expandoStore.events;
  var handle = expandoStore && expandoStore.handle;

  if (!handle) return; //no listeners registered

  if (!type) {
    for (type in events) {
      if (type !== '$destroy') {
        element.removeEventListener(type, handle);
      }
      delete events[type];
    }
  } else {

    var removeHandler = function(type) {
      var listenerFns = events[type];
      if (isDefined(fn)) {
        arrayRemove(listenerFns || [], fn);
      }
      if (!(isDefined(fn) && listenerFns && listenerFns.length > 0)) {
        element.removeEventListener(type, handle);
        delete events[type];
      }
    };

    forEach(type.split(' '), function(type) {
      removeHandler(type);
      if (MOUSE_EVENT_MAP[type]) {
        removeHandler(MOUSE_EVENT_MAP[type]);
      }
    });
  }
}

function jqLiteRemoveData(element, name) {
  var expandoId = element.ng339;
  var expandoStore = expandoId && jqCache[expandoId];

  if (expandoStore) {
    if (name) {
      delete expandoStore.data[name];
      return;
    }

    if (expandoStore.handle) {
      if (expandoStore.events.$destroy) {
        expandoStore.handle({}, '$destroy');
      }
      jqLiteOff(element);
    }
    delete jqCache[expandoId];
    element.ng339 = undefined; // don't delete DOM expandos. IE and Chrome don't like it
  }
}


function jqLiteExpandoStore(element, createIfNecessary) {
  var expandoId = element.ng339,
      expandoStore = expandoId && jqCache[expandoId];

  if (createIfNecessary && !expandoStore) {
    element.ng339 = expandoId = jqNextId();
    expandoStore = jqCache[expandoId] = {events: {}, data: {}, handle: undefined};
  }

  return expandoStore;
}


function jqLiteData(element, key, value) {
  if (jqLiteAcceptsData(element)) {
    var prop;

    var isSimpleSetter = isDefined(value);
    var isSimpleGetter = !isSimpleSetter && key && !isObject(key);
    var massGetter = !key;
    var expandoStore = jqLiteExpandoStore(element, !isSimpleGetter);
    var data = expandoStore && expandoStore.data;

    if (isSimpleSetter) { // data('key', value)
      data[kebabToCamel(key)] = value;
    } else {
      if (massGetter) {  // data()
        return data;
      } else {
        if (isSimpleGetter) { // data('key')
          // don't force creation of expandoStore if it doesn't exist yet
          return data && data[kebabToCamel(key)];
        } else { // mass-setter: data({key1: val1, key2: val2})
          for (prop in key) {
            data[kebabToCamel(prop)] = key[prop];
          }
        }
      }
    }
  }
}

function jqLiteHasClass(element, selector) {
  if (!element.getAttribute) return false;
  return ((' ' + (element.getAttribute('class') || '') + ' ').replace(/[\n\t]/g, ' ').
      indexOf(' ' + selector + ' ') > -1);
}

function jqLiteRemoveClass(element, cssClasses) {
  if (cssClasses && element.setAttribute) {
    forEach(cssClasses.split(' '), function(cssClass) {
      element.setAttribute('class', trim(
          (' ' + (element.getAttribute('class') || '') + ' ')
          .replace(/[\n\t]/g, ' ')
          .replace(' ' + trim(cssClass) + ' ', ' '))
      );
    });
  }
}

function jqLiteAddClass(element, cssClasses) {
  if (cssClasses && element.setAttribute) {
    var existingClasses = (' ' + (element.getAttribute('class') || '') + ' ')
                            .replace(/[\n\t]/g, ' ');

    forEach(cssClasses.split(' '), function(cssClass) {
      cssClass = trim(cssClass);
      if (existingClasses.indexOf(' ' + cssClass + ' ') === -1) {
        existingClasses += cssClass + ' ';
      }
    });

    element.setAttribute('class', trim(existingClasses));
  }
}


function jqLiteAddNodes(root, elements) {
  // THIS CODE IS VERY HOT. Don't make changes without benchmarking.

  if (elements) {

    // if a Node (the most common case)
    if (elements.nodeType) {
      root[root.length++] = elements;
    } else {
      var length = elements.length;

      // if an Array or NodeList and not a Window
      if (typeof length === 'number' && elements.window !== elements) {
        if (length) {
          for (var i = 0; i < length; i++) {
            root[root.length++] = elements[i];
          }
        }
      } else {
        root[root.length++] = elements;
      }
    }
  }
}


function jqLiteController(element, name) {
  return jqLiteInheritedData(element, '$' + (name || 'ngController') + 'Controller');
}

function jqLiteInheritedData(element, name, value) {
  // if element is the document object work with the html element instead
  // this makes $(document).scope() possible
  if (element.nodeType === NODE_TYPE_DOCUMENT) {
    element = element.documentElement;
  }
  var names = isArray(name) ? name : [name];

  while (element) {
    for (var i = 0, ii = names.length; i < ii; i++) {
      if (isDefined(value = jqLite.data(element, names[i]))) return value;
    }

    // If dealing with a document fragment node with a host element, and no parent, use the host
    // element as the parent. This enables directives within a Shadow DOM or polyfilled Shadow DOM
    // to lookup parent controllers.
    element = element.parentNode || (element.nodeType === NODE_TYPE_DOCUMENT_FRAGMENT && element.host);
  }
}

function jqLiteEmpty(element) {
  jqLiteDealoc(element, true);
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
}

function jqLiteRemove(element, keepData) {
  if (!keepData) jqLiteDealoc(element);
  var parent = element.parentNode;
  if (parent) parent.removeChild(element);
}


function jqLiteDocumentLoaded(action, win) {
  win = win || window;
  if (win.document.readyState === 'complete') {
    // Force the action to be run async for consistent behavior
    // from the action's point of view
    // i.e. it will definitely not be in a $apply
    win.setTimeout(action);
  } else {
    // No need to unbind this handler as load is only ever called once
    jqLite(win).on('load', action);
  }
}

function jqLiteReady(fn) {
  function trigger() {
    window.document.removeEventListener('DOMContentLoaded', trigger);
    window.removeEventListener('load', trigger);
    fn();
  }

  // check if document is already loaded
  if (window.document.readyState === 'complete') {
    window.setTimeout(fn);
  } else {
    // We can not use jqLite since we are not done loading and jQuery could be loaded later.

    // Works for modern browsers and IE9
    window.document.addEventListener('DOMContentLoaded', trigger);

    // Fallback to window.onload for others
    window.addEventListener('load', trigger);
  }
}

//////////////////////////////////////////
// Functions which are declared directly.
//////////////////////////////////////////
var JQLitePrototype = JQLite.prototype = {
  ready: jqLiteReady,
  toString: function() {
    var value = [];
    forEach(this, function(e) { value.push('' + e);});
    return '[' + value.join(', ') + ']';
  },

  eq: function(index) {
      return (index >= 0) ? jqLite(this[index]) : jqLite(this[this.length + index]);
  },

  length: 0,
  push: push,
  sort: [].sort,
  splice: [].splice
};

//////////////////////////////////////////
// Functions iterating getter/setters.
// these functions return self on setter and
// value on get.
//////////////////////////////////////////
var BOOLEAN_ATTR = {};
forEach('multiple,selected,checked,disabled,readOnly,required,open'.split(','), function(value) {
  BOOLEAN_ATTR[lowercase(value)] = value;
});
var BOOLEAN_ELEMENTS = {};
forEach('input,select,option,textarea,button,form,details'.split(','), function(value) {
  BOOLEAN_ELEMENTS[value] = true;
});
var ALIASED_ATTR = {
  'ngMinlength': 'minlength',
  'ngMaxlength': 'maxlength',
  'ngMin': 'min',
  'ngMax': 'max',
  'ngPattern': 'pattern',
  'ngStep': 'step'
};

function getBooleanAttrName(element, name) {
  // check dom last since we will most likely fail on name
  var booleanAttr = BOOLEAN_ATTR[name.toLowerCase()];

  // booleanAttr is here twice to minimize DOM access
  return booleanAttr && BOOLEAN_ELEMENTS[nodeName_(element)] && booleanAttr;
}

function getAliasedAttrName(name) {
  return ALIASED_ATTR[name];
}

forEach({
  data: jqLiteData,
  removeData: jqLiteRemoveData,
  hasData: jqLiteHasData,
  cleanData: jqLiteCleanData
}, function(fn, name) {
  JQLite[name] = fn;
});

forEach({
  data: jqLiteData,
  inheritedData: jqLiteInheritedData,

  scope: function(element) {
    // Can't use jqLiteData here directly so we stay compatible with jQuery!
    return jqLite.data(element, '$scope') || jqLiteInheritedData(element.parentNode || element, ['$isolateScope', '$scope']);
  },

  isolateScope: function(element) {
    // Can't use jqLiteData here directly so we stay compatible with jQuery!
    return jqLite.data(element, '$isolateScope') || jqLite.data(element, '$isolateScopeNoTemplate');
  },

  controller: jqLiteController,

  injector: function(element) {
    return jqLiteInheritedData(element, '$injector');
  },

  removeAttr: function(element, name) {
    element.removeAttribute(name);
  },

  hasClass: jqLiteHasClass,

  css: function(element, name, value) {
    name = cssKebabToCamel(name);

    if (isDefined(value)) {
      element.style[name] = value;
    } else {
      return element.style[name];
    }
  },

  attr: function(element, name, value) {
    var ret;
    var nodeType = element.nodeType;
    if (nodeType === NODE_TYPE_TEXT || nodeType === NODE_TYPE_ATTRIBUTE || nodeType === NODE_TYPE_COMMENT ||
      !element.getAttribute) {
      return;
    }

    var lowercasedName = lowercase(name);
    var isBooleanAttr = BOOLEAN_ATTR[lowercasedName];

    if (isDefined(value)) {
      // setter

      if (value === null || (value === false && isBooleanAttr)) {
        element.removeAttribute(name);
      } else {
        element.setAttribute(name, isBooleanAttr ? lowercasedName : value);
      }
    } else {
      // getter

      ret = element.getAttribute(name);

      if (isBooleanAttr && ret !== null) {
        ret = lowercasedName;
      }
      // Normalize non-existing attributes to undefined (as jQuery).
      return ret === null ? undefined : ret;
    }
  },

  prop: function(element, name, value) {
    if (isDefined(value)) {
      element[name] = value;
    } else {
      return element[name];
    }
  },

  text: (function() {
    getText.$dv = '';
    return getText;

    function getText(element, value) {
      if (isUndefined(value)) {
        var nodeType = element.nodeType;
        return (nodeType === NODE_TYPE_ELEMENT || nodeType === NODE_TYPE_TEXT) ? element.textContent : '';
      }
      element.textContent = value;
    }
  })(),

  val: function(element, value) {
    if (isUndefined(value)) {
      if (element.multiple && nodeName_(element) === 'select') {
        var result = [];
        forEach(element.options, function(option) {
          if (option.selected) {
            result.push(option.value || option.text);
          }
        });
        return result;
      }
      return element.value;
    }
    element.value = value;
  },

  html: function(element, value) {
    if (isUndefined(value)) {
      return element.innerHTML;
    }
    jqLiteDealoc(element, true);
    element.innerHTML = value;
  },

  empty: jqLiteEmpty
}, function(fn, name) {
  /**
   * Properties: writes return selection, reads return first value
   */
  JQLite.prototype[name] = function(arg1, arg2) {
    var i, key;
    var nodeCount = this.length;

    // jqLiteHasClass has only two arguments, but is a getter-only fn, so we need to special-case it
    // in a way that survives minification.
    // jqLiteEmpty takes no arguments but is a setter.
    if (fn !== jqLiteEmpty &&
        (isUndefined((fn.length === 2 && (fn !== jqLiteHasClass && fn !== jqLiteController)) ? arg1 : arg2))) {
      if (isObject(arg1)) {

        // we are a write, but the object properties are the key/values
        for (i = 0; i < nodeCount; i++) {
          if (fn === jqLiteData) {
            // data() takes the whole object in jQuery
            fn(this[i], arg1);
          } else {
            for (key in arg1) {
              fn(this[i], key, arg1[key]);
            }
          }
        }
        // return self for chaining
        return this;
      } else {
        // we are a read, so read the first child.
        // TODO: do we still need this?
        var value = fn.$dv;
        // Only if we have $dv do we iterate over all, otherwise it is just the first element.
        var jj = (isUndefined(value)) ? Math.min(nodeCount, 1) : nodeCount;
        for (var j = 0; j < jj; j++) {
          var nodeValue = fn(this[j], arg1, arg2);
          value = value ? value + nodeValue : nodeValue;
        }
        return value;
      }
    } else {
      // we are a write, so apply to all children
      for (i = 0; i < nodeCount; i++) {
        fn(this[i], arg1, arg2);
      }
      // return self for chaining
      return this;
    }
  };
});

function createEventHandler(element, events) {
  var eventHandler = function(event, type) {
    // jQuery specific api
    event.isDefaultPrevented = function() {
      return event.defaultPrevented;
    };

    var eventFns = events[type || event.type];
    var eventFnsLength = eventFns ? eventFns.length : 0;

    if (!eventFnsLength) return;

    if (isUndefined(event.immediatePropagationStopped)) {
      var originalStopImmediatePropagation = event.stopImmediatePropagation;
      event.stopImmediatePropagation = function() {
        event.immediatePropagationStopped = true;

        if (event.stopPropagation) {
          event.stopPropagation();
        }

        if (originalStopImmediatePropagation) {
          originalStopImmediatePropagation.call(event);
        }
      };
    }

    event.isImmediatePropagationStopped = function() {
      return event.immediatePropagationStopped === true;
    };

    // Some events have special handlers that wrap the real handler
    var handlerWrapper = eventFns.specialHandlerWrapper || defaultHandlerWrapper;

    // Copy event handlers in case event handlers array is modified during execution.
    if ((eventFnsLength > 1)) {
      eventFns = shallowCopy(eventFns);
    }

    for (var i = 0; i < eventFnsLength; i++) {
      if (!event.isImmediatePropagationStopped()) {
        handlerWrapper(element, event, eventFns[i]);
      }
    }
  };

  // TODO: this is a hack for angularMocks/clearDataCache that makes it possible to deregister all
  //       events on `element`
  eventHandler.elem = element;
  return eventHandler;
}

function defaultHandlerWrapper(element, event, handler) {
  handler.call(element, event);
}

function specialMouseHandlerWrapper(target, event, handler) {
  // Refer to jQuery's implementation of mouseenter & mouseleave
  // Read about mouseenter and mouseleave:
  // http://www.quirksmode.org/js/events_mouse.html#link8
  var related = event.relatedTarget;
  // For mousenter/leave call the handler if related is outside the target.
  // NB: No relatedTarget if the mouse left/entered the browser window
  if (!related || (related !== target && !jqLiteContains.call(target, related))) {
    handler.call(target, event);
  }
}

//////////////////////////////////////////
// Functions iterating traversal.
// These functions chain results into a single
// selector.
//////////////////////////////////////////
forEach({
  removeData: jqLiteRemoveData,

  on: function jqLiteOn(element, type, fn, unsupported) {
    if (isDefined(unsupported)) throw jqLiteMinErr('onargs', 'jqLite#on() does not support the `selector` or `eventData` parameters');

    // Do not add event handlers to non-elements because they will not be cleaned up.
    if (!jqLiteAcceptsData(element)) {
      return;
    }

    var expandoStore = jqLiteExpandoStore(element, true);
    var events = expandoStore.events;
    var handle = expandoStore.handle;

    if (!handle) {
      handle = expandoStore.handle = createEventHandler(element, events);
    }

    // http://jsperf.com/string-indexof-vs-split
    var types = type.indexOf(' ') >= 0 ? type.split(' ') : [type];
    var i = types.length;

    var addHandler = function(type, specialHandlerWrapper, noEventListener) {
      var eventFns = events[type];

      if (!eventFns) {
        eventFns = events[type] = [];
        eventFns.specialHandlerWrapper = specialHandlerWrapper;
        if (type !== '$destroy' && !noEventListener) {
          element.addEventListener(type, handle);
        }
      }

      eventFns.push(fn);
    };

    while (i--) {
      type = types[i];
      if (MOUSE_EVENT_MAP[type]) {
        addHandler(MOUSE_EVENT_MAP[type], specialMouseHandlerWrapper);
        addHandler(type, undefined, true);
      } else {
        addHandler(type);
      }
    }
  },

  off: jqLiteOff,

  one: function(element, type, fn) {
    element = jqLite(element);

    //add the listener twice so that when it is called
    //you can remove the original function and still be
    //able to call element.off(ev, fn) normally
    element.on(type, function onFn() {
      element.off(type, fn);
      element.off(type, onFn);
    });
    element.on(type, fn);
  },

  replaceWith: function(element, replaceNode) {
    var index, parent = element.parentNode;
    jqLiteDealoc(element);
    forEach(new JQLite(replaceNode), function(node) {
      if (index) {
        parent.insertBefore(node, index.nextSibling);
      } else {
        parent.replaceChild(node, element);
      }
      index = node;
    });
  },

  children: function(element) {
    var children = [];
    forEach(element.childNodes, function(element) {
      if (element.nodeType === NODE_TYPE_ELEMENT) {
        children.push(element);
      }
    });
    return children;
  },

  contents: function(element) {
    return element.contentDocument || element.childNodes || [];
  },

  append: function(element, node) {
    var nodeType = element.nodeType;
    if (nodeType !== NODE_TYPE_ELEMENT && nodeType !== NODE_TYPE_DOCUMENT_FRAGMENT) return;

    node = new JQLite(node);

    for (var i = 0, ii = node.length; i < ii; i++) {
      var child = node[i];
      element.appendChild(child);
    }
  },

  prepend: function(element, node) {
    if (element.nodeType === NODE_TYPE_ELEMENT) {
      var index = element.firstChild;
      forEach(new JQLite(node), function(child) {
        element.insertBefore(child, index);
      });
    }
  },

  wrap: function(element, wrapNode) {
    jqLiteWrapNode(element, jqLite(wrapNode).eq(0).clone()[0]);
  },

  remove: jqLiteRemove,

  detach: function(element) {
    jqLiteRemove(element, true);
  },

  after: function(element, newElement) {
    var index = element, parent = element.parentNode;

    if (parent) {
      newElement = new JQLite(newElement);

      for (var i = 0, ii = newElement.length; i < ii; i++) {
        var node = newElement[i];
        parent.insertBefore(node, index.nextSibling);
        index = node;
      }
    }
  },

  addClass: jqLiteAddClass,
  removeClass: jqLiteRemoveClass,

  toggleClass: function(element, selector, condition) {
    if (selector) {
      forEach(selector.split(' '), function(className) {
        var classCondition = condition;
        if (isUndefined(classCondition)) {
          classCondition = !jqLiteHasClass(element, className);
        }
        (classCondition ? jqLiteAddClass : jqLiteRemoveClass)(element, className);
      });
    }
  },

  parent: function(element) {
    var parent = element.parentNode;
    return parent && parent.nodeType !== NODE_TYPE_DOCUMENT_FRAGMENT ? parent : null;
  },

  next: function(element) {
    return element.nextElementSibling;
  },

  find: function(element, selector) {
    if (element.getElementsByTagName) {
      return element.getElementsByTagName(selector);
    } else {
      return [];
    }
  },

  clone: jqLiteClone,

  triggerHandler: function(element, event, extraParameters) {

    var dummyEvent, eventFnsCopy, handlerArgs;
    var eventName = event.type || event;
    var expandoStore = jqLiteExpandoStore(element);
    var events = expandoStore && expandoStore.events;
    var eventFns = events && events[eventName];

    if (eventFns) {
      // Create a dummy event to pass to the handlers
      dummyEvent = {
        preventDefault: function() { this.defaultPrevented = true; },
        isDefaultPrevented: function() { return this.defaultPrevented === true; },
        stopImmediatePropagation: function() { this.immediatePropagationStopped = true; },
        isImmediatePropagationStopped: function() { return this.immediatePropagationStopped === true; },
        stopPropagation: noop,
        type: eventName,
        target: element
      };

      // If a custom event was provided then extend our dummy event with it
      if (event.type) {
        dummyEvent = extend(dummyEvent, event);
      }

      // Copy event handlers in case event handlers array is modified during execution.
      eventFnsCopy = shallowCopy(eventFns);
      handlerArgs = extraParameters ? [dummyEvent].concat(extraParameters) : [dummyEvent];

      forEach(eventFnsCopy, function(fn) {
        if (!dummyEvent.isImmediatePropagationStopped()) {
          fn.apply(element, handlerArgs);
        }
      });
    }
  }
}, function(fn, name) {
  /**
   * chaining functions
   */
  JQLite.prototype[name] = function(arg1, arg2, arg3) {
    var value;

    for (var i = 0, ii = this.length; i < ii; i++) {
      if (isUndefined(value)) {
        value = fn(this[i], arg1, arg2, arg3);
        if (isDefined(value)) {
          // any function which returns a value needs to be wrapped
          value = jqLite(value);
        }
      } else {
        jqLiteAddNodes(value, fn(this[i], arg1, arg2, arg3));
      }
    }
    return isDefined(value) ? value : this;
  };
});

// bind legacy bind/unbind to on/off
JQLite.prototype.bind = JQLite.prototype.on;
JQLite.prototype.unbind = JQLite.prototype.off;


// Provider for private $$jqLite service
/** @this */
function $$jqLiteProvider() {
  this.$get = function $$jqLite() {
    return extend(JQLite, {
      hasClass: function(node, classes) {
        if (node.attr) node = node[0];
        return jqLiteHasClass(node, classes);
      },
      addClass: function(node, classes) {
        if (node.attr) node = node[0];
        return jqLiteAddClass(node, classes);
      },
      removeClass: function(node, classes) {
        if (node.attr) node = node[0];
        return jqLiteRemoveClass(node, classes);
      }
    });
  };
}

/**
 * Computes a hash of an 'obj'.
 * Hash of a:
 *  string is string
 *  number is number as string
 *  object is either result of calling $$hashKey function on the object or uniquely generated id,
 *         that is also assigned to the $$hashKey property of the object.
 *
 * @param obj
 * @returns {string} hash string such that the same input will have the same hash string.
 *         The resulting string key is in 'type:hashKey' format.
 */
function hashKey(obj, nextUidFn) {
  var key = obj && obj.$$hashKey;

  if (key) {
    if (typeof key === 'function') {
      key = obj.$$hashKey();
    }
    return key;
  }

  var objType = typeof obj;
  if (objType === 'function' || (objType === 'object' && obj !== null)) {
    key = obj.$$hashKey = objType + ':' + (nextUidFn || nextUid)();
  } else {
    key = objType + ':' + obj;
  }

  return key;
}

// A minimal ES2015 Map implementation.
// Should be bug/feature equivalent to the native implementations of supported browsers
// (for the features required in Angular).
// See https://kangax.github.io/compat-table/es6/#test-Map
var nanKey = Object.create(null);
function NgMapShim() {
  this._keys = [];
  this._values = [];
  this._lastKey = NaN;
  this._lastIndex = -1;
}
NgMapShim.prototype = {
  _idx: function(key) {
    if (key === this._lastKey) {
      return this._lastIndex;
    }
    this._lastKey = key;
    this._lastIndex = this._keys.indexOf(key);
    return this._lastIndex;
  },
  _transformKey: function(key) {
    return isNumberNaN(key) ? nanKey : key;
  },
  get: function(key) {
    key = this._transformKey(key);
    var idx = this._idx(key);
    if (idx !== -1) {
      return this._values[idx];
    }
  },
  set: function(key, value) {
    key = this._transformKey(key);
    var idx = this._idx(key);
    if (idx === -1) {
      idx = this._lastIndex = this._keys.length;
    }
    this._keys[idx] = key;
    this._values[idx] = value;

    // Support: IE11
    // Do not `return this` to simulate the partial IE11 implementation
  },
  delete: function(key) {
    key = this._transformKey(key);
    var idx = this._idx(key);
    if (idx === -1) {
      return false;
    }
    this._keys.splice(idx, 1);
    this._values.splice(idx, 1);
    this._lastKey = NaN;
    this._lastIndex = -1;
    return true;
  }
};

// For now, always use `NgMapShim`, even if `window.Map` is available. Some native implementations
// are still buggy (often in subtle ways) and can cause hard-to-debug failures. When native `Map`
// implementations get more stable, we can reconsider switching to `window.Map` (when available).
var NgMap = NgMapShim;

var $$MapProvider = [/** @this */function() {
  this.$get = [function() {
    return NgMap;
  }];
}];

/**
 * @ngdoc function
 * @module ng
 * @name angular.injector
 * @kind function
 *
 * @description
 * Creates an injector object that can be used for retrieving services as well as for
 * dependency injection (see {@link guide/di dependency injection}).
 *
 * @param {Array.<string|Function>} modules A list of module functions or their aliases. See
 *     {@link angular.module}. The `ng` module must be explicitly added.
 * @param {boolean=} [strictDi=false] Whether the injector should be in strict mode, which
 *     disallows argument name annotation inference.
 * @returns {injector} Injector object. See {@link auto.$injector $injector}.
 *
 * @example
 * Typical usage
 * ```js
 *   // create an injector
 *   var $injector = angular.injector(['ng']);
 *
 *   // use the injector to kick off your application
 *   // use the type inference to auto inject arguments, or use implicit injection
 *   $injector.invoke(function($rootScope, $compile, $document) {
 *     $compile($document)($rootScope);
 *     $rootScope.$digest();
 *   });
 * ```
 *
 * Sometimes you want to get access to the injector of a currently running Angular app
 * from outside Angular. Perhaps, you want to inject and compile some markup after the
 * application has been bootstrapped. You can do this using the extra `injector()` added
 * to JQuery/jqLite elements. See {@link angular.element}.
 *
 * *This is fairly rare but could be the case if a third party library is injecting the
 * markup.*
 *
 * In the following example a new block of HTML containing a `ng-controller`
 * directive is added to the end of the document body by JQuery. We then compile and link
 * it into the current AngularJS scope.
 *
 * ```js
 * var $div = $('<div ng-controller="MyCtrl">{{content.label}}</div>');
 * $(document.body).append($div);
 *
 * angular.element(document).injector().invoke(function($compile) {
 *   var scope = angular.element($div).scope();
 *   $compile($div)(scope);
 * });
 * ```
 */


/**
 * @ngdoc module
 * @name auto
 * @installation
 * @description
 *
 * Implicit module which gets automatically added to each {@link auto.$injector $injector}.
 */

var ARROW_ARG = /^([^(]+?)=>/;
var FN_ARGS = /^[^(]*\(\s*([^)]*)\)/m;
var FN_ARG_SPLIT = /,/;
var FN_ARG = /^\s*(_?)(\S+?)\1\s*$/;
var STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/mg;
var $injectorMinErr = minErr('$injector');

function stringifyFn(fn) {
  return Function.prototype.toString.call(fn);
}

function extractArgs(fn) {
  var fnText = stringifyFn(fn).replace(STRIP_COMMENTS, ''),
      args = fnText.match(ARROW_ARG) || fnText.match(FN_ARGS);
  return args;
}

function anonFn(fn) {
  // For anonymous functions, showing at the very least the function signature can help in
  // debugging.
  var args = extractArgs(fn);
  if (args) {
    return 'function(' + (args[1] || '').replace(/[\s\r\n]+/, ' ') + ')';
  }
  return 'fn';
}

function annotate(fn, strictDi, name) {
  var $inject,
      argDecl,
      last;

  if (typeof fn === 'function') {
    if (!($inject = fn.$inject)) {
      $inject = [];
      if (fn.length) {
        if (strictDi) {
          if (!isString(name) || !name) {
            name = fn.name || anonFn(fn);
          }
          throw $injectorMinErr('strictdi',
            '{0} is not using explicit annotation and cannot be invoked in strict mode', name);
        }
        argDecl = extractArgs(fn);
        forEach(argDecl[1].split(FN_ARG_SPLIT), function(arg) {
          arg.replace(FN_ARG, function(all, underscore, name) {
            $inject.push(name);
          });
        });
      }
      fn.$inject = $inject;
    }
  } else if (isArray(fn)) {
    last = fn.length - 1;
    assertArgFn(fn[last], 'fn');
    $inject = fn.slice(0, last);
  } else {
    assertArgFn(fn, 'fn', true);
  }
  return $inject;
}

///////////////////////////////////////

/**
 * @ngdoc service
 * @name $injector
 *
 * @description
 *
 * `$injector` is used to retrieve object instances as defined by
 * {@link auto.$provide provider}, instantiate types, invoke methods,
 * and load modules.
 *
 * The following always holds true:
 *
 * ```js
 *   var $injector = angular.injector();
 *   expect($injector.get('$injector')).toBe($injector);
 *   expect($injector.invoke(function($injector) {
 *     return $injector;
 *   })).toBe($injector);
 * ```
 *
 * # Injection Function Annotation
 *
 * JavaScript does not have annotations, and annotations are needed for dependency injection. The
 * following are all valid ways of annotating function with injection arguments and are equivalent.
 *
 * ```js
 *   // inferred (only works if code not minified/obfuscated)
 *   $injector.invoke(function(serviceA){});
 *
 *   // annotated
 *   function explicit(serviceA) {};
 *   explicit.$inject = ['serviceA'];
 *   $injector.invoke(explicit);
 *
 *   // inline
 *   $injector.invoke(['serviceA', function(serviceA){}]);
 * ```
 *
 * ## Inference
 *
 * In JavaScript calling `toString()` on a function returns the function definition. The definition
 * can then be parsed and the function arguments can be extracted. This method of discovering
 * annotations is disallowed when the injector is in strict mode.
 * *NOTE:* This does not work with minification, and obfuscation tools since these tools change the
 * argument names.
 *
 * ## `$inject` Annotation
 * By adding an `$inject` property onto a function the injection parameters can be specified.
 *
 * ## Inline
 * As an array of injection names, where the last item in the array is the function to call.
 */

/**
 * @ngdoc property
 * @name $injector#modules
 * @type {Object}
 * @description
 * A hash containing all the modules that have been loaded into the
 * $injector.
 *
 * You can use this property to find out information about a module via the
 * {@link angular.Module#info `myModule.info(...)`} method.
 *
 * For example:
 *
 * ```
 * var info = $injector.modules['ngAnimate'].info();
 * ```
 *
 * **Do not use this property to attempt to modify the modules after the application
 * has been bootstrapped.**
 */


/**
 * @ngdoc method
 * @name $injector#get
 *
 * @description
 * Return an instance of the service.
 *
 * @param {string} name The name of the instance to retrieve.
 * @param {string=} caller An optional string to provide the origin of the function call for error messages.
 * @return {*} The instance.
 */

/**
 * @ngdoc method
 * @name $injector#invoke
 *
 * @description
 * Invoke the method and supply the method arguments from the `$injector`.
 *
 * @param {Function|Array.<string|Function>} fn The injectable function to invoke. Function parameters are
 *   injected according to the {@link guide/di $inject Annotation} rules.
 * @param {Object=} self The `this` for the invoked method.
 * @param {Object=} locals Optional object. If preset then any argument names are read from this
 *                         object first, before the `$injector` is consulted.
 * @returns {*} the value returned by the invoked `fn` function.
 */

/**
 * @ngdoc method
 * @name $injector#has
 *
 * @description
 * Allows the user to query if the particular service exists.
 *
 * @param {string} name Name of the service to query.
 * @returns {boolean} `true` if injector has given service.
 */

/**
 * @ngdoc method
 * @name $injector#instantiate
 * @description
 * Create a new instance of JS type. The method takes a constructor function, invokes the new
 * operator, and supplies all of the arguments to the constructor function as specified by the
 * constructor annotation.
 *
 * @param {Function} Type Annotated constructor function.
 * @param {Object=} locals Optional object. If preset then any argument names are read from this
 * object first, before the `$injector` is consulted.
 * @returns {Object} new instance of `Type`.
 */

/**
 * @ngdoc method
 * @name $injector#annotate
 *
 * @description
 * Returns an array of service names which the function is requesting for injection. This API is
 * used by the injector to determine which services need to be injected into the function when the
 * function is invoked. There are three ways in which the function can be annotated with the needed
 * dependencies.
 *
 * # Argument names
 *
 * The simplest form is to extract the dependencies from the arguments of the function. This is done
 * by converting the function into a string using `toString()` method and extracting the argument
 * names.
 * ```js
 *   // Given
 *   function MyController($scope, $route) {
 *     // ...
 *   }
 *
 *   // Then
 *   expect(injector.annotate(MyController)).toEqual(['$scope', '$route']);
 * ```
 *
 * You can disallow this method by using strict injection mode.
 *
 * This method does not work with code minification / obfuscation. For this reason the following
 * annotation strategies are supported.
 *
 * # The `$inject` property
 *
 * If a function has an `$inject` property and its value is an array of strings, then the strings
 * represent names of services to be injected into the function.
 * ```js
 *   // Given
 *   var MyController = function(obfuscatedScope, obfuscatedRoute) {
 *     // ...
 *   }
 *   // Define function dependencies
 *   MyController['$inject'] = ['$scope', '$route'];
 *
 *   // Then
 *   expect(injector.annotate(MyController)).toEqual(['$scope', '$route']);
 * ```
 *
 * # The array notation
 *
 * It is often desirable to inline Injected functions and that's when setting the `$inject` property
 * is very inconvenient. In these situations using the array notation to specify the dependencies in
 * a way that survives minification is a better choice:
 *
 * ```js
 *   // We wish to write this (not minification / obfuscation safe)
 *   injector.invoke(function($compile, $rootScope) {
 *     // ...
 *   });
 *
 *   // We are forced to write break inlining
 *   var tmpFn = function(obfuscatedCompile, obfuscatedRootScope) {
 *     // ...
 *   };
 *   tmpFn.$inject = ['$compile', '$rootScope'];
 *   injector.invoke(tmpFn);
 *
 *   // To better support inline function the inline annotation is supported
 *   injector.invoke(['$compile', '$rootScope', function(obfCompile, obfRootScope) {
 *     // ...
 *   }]);
 *
 *   // Therefore
 *   expect(injector.annotate(
 *      ['$compile', '$rootScope', function(obfus_$compile, obfus_$rootScope) {}])
 *    ).toEqual(['$compile', '$rootScope']);
 * ```
 *
 * @param {Function|Array.<string|Function>} fn Function for which dependent service names need to
 * be retrieved as described above.
 *
 * @param {boolean=} [strictDi=false] Disallow argument name annotation inference.
 *
 * @returns {Array.<string>} The names of the services which the function requires.
 */



/**
 * @ngdoc service
 * @name $provide
 *
 * @description
 *
 * The {@link auto.$provide $provide} service has a number of methods for registering components
 * with the {@link auto.$injector $injector}. Many of these functions are also exposed on
 * {@link angular.Module}.
 *
 * An Angular **service** is a singleton object created by a **service factory**.  These **service
 * factories** are functions which, in turn, are created by a **service provider**.
 * The **service providers** are constructor functions. When instantiated they must contain a
 * property called `$get`, which holds the **service factory** function.
 *
 * When you request a service, the {@link auto.$injector $injector} is responsible for finding the
 * correct **service provider**, instantiating it and then calling its `$get` **service factory**
 * function to get the instance of the **service**.
 *
 * Often services have no configuration options and there is no need to add methods to the service
 * provider.  The provider will be no more than a constructor function with a `$get` property. For
 * these cases the {@link auto.$provide $provide} service has additional helper methods to register
 * services without specifying a provider.
 *
 * * {@link auto.$provide#provider provider(name, provider)} - registers a **service provider** with the
 *     {@link auto.$injector $injector}
 * * {@link auto.$provide#constant constant(name, obj)} - registers a value/object that can be accessed by
 *     providers and services.
 * * {@link auto.$provide#value value(name, obj)} - registers a value/object that can only be accessed by
 *     services, not providers.
 * * {@link auto.$provide#factory factory(name, fn)} - registers a service **factory function**
 *     that will be wrapped in a **service provider** object, whose `$get` property will contain the
 *     given factory function.
 * * {@link auto.$provide#service service(name, Fn)} - registers a **constructor function**
 *     that will be wrapped in a **service provider** object, whose `$get` property will instantiate
 *      a new object using the given constructor function.
 * * {@link auto.$provide#decorator decorator(name, decorFn)} - registers a **decorator function** that
 *      will be able to modify or replace the implementation of another service.
 *
 * See the individual methods for more information and examples.
 */

/**
 * @ngdoc method
 * @name $provide#provider
 * @description
 *
 * Register a **provider function** with the {@link auto.$injector $injector}. Provider functions
 * are constructor functions, whose instances are responsible for "providing" a factory for a
 * service.
 *
 * Service provider names start with the name of the service they provide followed by `Provider`.
 * For example, the {@link ng.$log $log} service has a provider called
 * {@link ng.$logProvider $logProvider}.
 *
 * Service provider objects can have additional methods which allow configuration of the provider
 * and its service. Importantly, you can configure what kind of service is created by the `$get`
 * method, or how that service will act. For example, the {@link ng.$logProvider $logProvider} has a
 * method {@link ng.$logProvider#debugEnabled debugEnabled}
 * which lets you specify whether the {@link ng.$log $log} service will log debug messages to the
 * console or not.
 *
 * @param {string} name The name of the instance. NOTE: the provider will be available under `name +
                        'Provider'` key.
 * @param {(Object|function())} provider If the provider is:
 *
 *   - `Object`: then it should have a `$get` method. The `$get` method will be invoked using
 *     {@link auto.$injector#invoke $injector.invoke()} when an instance needs to be created.
 *   - `Constructor`: a new instance of the provider will be created using
 *     {@link auto.$injector#instantiate $injector.instantiate()}, then treated as `object`.
 *
 * @returns {Object} registered provider instance

 * @example
 *
 * The following example shows how to create a simple event tracking service and register it using
 * {@link auto.$provide#provider $provide.provider()}.
 *
 * ```js
 *  // Define the eventTracker provider
 *  function EventTrackerProvider() {
 *    var trackingUrl = '/track';
 *
 *    // A provider method for configuring where the tracked events should been saved
 *    this.setTrackingUrl = function(url) {
 *      trackingUrl = url;
 *    };
 *
 *    // The service factory function
 *    this.$get = ['$http', function($http) {
 *      var trackedEvents = {};
 *      return {
 *        // Call this to track an event
 *        event: function(event) {
 *          var count = trackedEvents[event] || 0;
 *          count += 1;
 *          trackedEvents[event] = count;
 *          return count;
 *        },
 *        // Call this to save the tracked events to the trackingUrl
 *        save: function() {
 *          $http.post(trackingUrl, trackedEvents);
 *        }
 *      };
 *    }];
 *  }
 *
 *  describe('eventTracker', function() {
 *    var postSpy;
 *
 *    beforeEach(module(function($provide) {
 *      // Register the eventTracker provider
 *      $provide.provider('eventTracker', EventTrackerProvider);
 *    }));
 *
 *    beforeEach(module(function(eventTrackerProvider) {
 *      // Configure eventTracker provider
 *      eventTrackerProvider.setTrackingUrl('/custom-track');
 *    }));
 *
 *    it('tracks events', inject(function(eventTracker) {
 *      expect(eventTracker.event('login')).toEqual(1);
 *      expect(eventTracker.event('login')).toEqual(2);
 *    }));
 *
 *    it('saves to the tracking url', inject(function(eventTracker, $http) {
 *      postSpy = spyOn($http, 'post');
 *      eventTracker.event('login');
 *      eventTracker.save();
 *      expect(postSpy).toHaveBeenCalled();
 *      expect(postSpy.mostRecentCall.args[0]).not.toEqual('/track');
 *      expect(postSpy.mostRecentCall.args[0]).toEqual('/custom-track');
 *      expect(postSpy.mostRecentCall.args[1]).toEqual({ 'login': 1 });
 *    }));
 *  });
 * ```
 */

/**
 * @ngdoc method
 * @name $provide#factory
 * @description
 *
 * Register a **service factory**, which will be called to return the service instance.
 * This is short for registering a service where its provider consists of only a `$get` property,
 * which is the given service factory function.
 * You should use {@link auto.$provide#factory $provide.factory(getFn)} if you do not need to
 * configure your service in a provider.
 *
 * @param {string} name The name of the instance.
 * @param {Function|Array.<string|Function>} $getFn The injectable $getFn for the instance creation.
 *                      Internally this is a short hand for `$provide.provider(name, {$get: $getFn})`.
 * @returns {Object} registered provider instance
 *
 * @example
 * Here is an example of registering a service
 * ```js
 *   $provide.factory('ping', ['$http', function($http) {
 *     return function ping() {
 *       return $http.send('/ping');
 *     };
 *   }]);
 * ```
 * You would then inject and use this service like this:
 * ```js
 *   someModule.controller('Ctrl', ['ping', function(ping) {
 *     ping();
 *   }]);
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#service
 * @description
 *
 * Register a **service constructor**, which will be invoked with `new` to create the service
 * instance.
 * This is short for registering a service where its provider's `$get` property is a factory
 * function that returns an instance instantiated by the injector from the service constructor
 * function.
 *
 * Internally it looks a bit like this:
 *
 * ```
 * {
 *   $get: function() {
 *     return $injector.instantiate(constructor);
 *   }
 * }
 * ```
 *
 *
 * You should use {@link auto.$provide#service $provide.service(class)} if you define your service
 * as a type/class.
 *
 * @param {string} name The name of the instance.
 * @param {Function|Array.<string|Function>} constructor An injectable class (constructor function)
 *     that will be instantiated.
 * @returns {Object} registered provider instance
 *
 * @example
 * Here is an example of registering a service using
 * {@link auto.$provide#service $provide.service(class)}.
 * ```js
 *   var Ping = function($http) {
 *     this.$http = $http;
 *   };
 *
 *   Ping.$inject = ['$http'];
 *
 *   Ping.prototype.send = function() {
 *     return this.$http.get('/ping');
 *   };
 *   $provide.service('ping', Ping);
 * ```
 * You would then inject and use this service like this:
 * ```js
 *   someModule.controller('Ctrl', ['ping', function(ping) {
 *     ping.send();
 *   }]);
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#value
 * @description
 *
 * Register a **value service** with the {@link auto.$injector $injector}, such as a string, a
 * number, an array, an object or a function. This is short for registering a service where its
 * provider's `$get` property is a factory function that takes no arguments and returns the **value
 * service**. That also means it is not possible to inject other services into a value service.
 *
 * Value services are similar to constant services, except that they cannot be injected into a
 * module configuration function (see {@link angular.Module#config}) but they can be overridden by
 * an Angular {@link auto.$provide#decorator decorator}.
 *
 * @param {string} name The name of the instance.
 * @param {*} value The value.
 * @returns {Object} registered provider instance
 *
 * @example
 * Here are some examples of creating value services.
 * ```js
 *   $provide.value('ADMIN_USER', 'admin');
 *
 *   $provide.value('RoleLookup', { admin: 0, writer: 1, reader: 2 });
 *
 *   $provide.value('halfOf', function(value) {
 *     return value / 2;
 *   });
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#constant
 * @description
 *
 * Register a **constant service** with the {@link auto.$injector $injector}, such as a string,
 * a number, an array, an object or a function. Like the {@link auto.$provide#value value}, it is not
 * possible to inject other services into a constant.
 *
 * But unlike {@link auto.$provide#value value}, a constant can be
 * injected into a module configuration function (see {@link angular.Module#config}) and it cannot
 * be overridden by an Angular {@link auto.$provide#decorator decorator}.
 *
 * @param {string} name The name of the constant.
 * @param {*} value The constant value.
 * @returns {Object} registered instance
 *
 * @example
 * Here a some examples of creating constants:
 * ```js
 *   $provide.constant('SHARD_HEIGHT', 306);
 *
 *   $provide.constant('MY_COLOURS', ['red', 'blue', 'grey']);
 *
 *   $provide.constant('double', function(value) {
 *     return value * 2;
 *   });
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#decorator
 * @description
 *
 * Register a **decorator function** with the {@link auto.$injector $injector}. A decorator function
 * intercepts the creation of a service, allowing it to override or modify the behavior of the
 * service. The return value of the decorator function may be the original service, or a new service
 * that replaces (or wraps and delegates to) the original service.
 *
 * You can find out more about using decorators in the {@link guide/decorators} guide.
 *
 * @param {string} name The name of the service to decorate.
 * @param {Function|Array.<string|Function>} decorator This function will be invoked when the service needs to be
 *    provided and should return the decorated service instance. The function is called using
 *    the {@link auto.$injector#invoke injector.invoke} method and is therefore fully injectable.
 *    Local injection arguments:
 *
 *    * `$delegate` - The original service instance, which can be replaced, monkey patched, configured,
 *      decorated or delegated to.
 *
 * @example
 * Here we decorate the {@link ng.$log $log} service to convert warnings to errors by intercepting
 * calls to {@link ng.$log#error $log.warn()}.
 * ```js
 *   $provide.decorator('$log', ['$delegate', function($delegate) {
 *     $delegate.warn = $delegate.error;
 *     return $delegate;
 *   }]);
 * ```
 */


function createInjector(modulesToLoad, strictDi) {
  strictDi = (strictDi === true);
  var INSTANTIATING = {},
      providerSuffix = 'Provider',
      path = [],
      loadedModules = new NgMap(),
      providerCache = {
        $provide: {
            provider: supportObject(provider),
            factory: supportObject(factory),
            service: supportObject(service),
            value: supportObject(value),
            constant: supportObject(constant),
            decorator: decorator
          }
      },
      providerInjector = (providerCache.$injector =
          createInternalInjector(providerCache, function(serviceName, caller) {
            if (angular.isString(caller)) {
              path.push(caller);
            }
            throw $injectorMinErr('unpr', 'Unknown provider: {0}', path.join(' <- '));
          })),
      instanceCache = {},
      protoInstanceInjector =
          createInternalInjector(instanceCache, function(serviceName, caller) {
            var provider = providerInjector.get(serviceName + providerSuffix, caller);
            return instanceInjector.invoke(
                provider.$get, provider, undefined, serviceName);
          }),
      instanceInjector = protoInstanceInjector;

  providerCache['$injector' + providerSuffix] = { $get: valueFn(protoInstanceInjector) };
  instanceInjector.modules = providerInjector.modules = createMap();
  var runBlocks = loadModules(modulesToLoad);
  instanceInjector = protoInstanceInjector.get('$injector');
  instanceInjector.strictDi = strictDi;
  forEach(runBlocks, function(fn) { if (fn) instanceInjector.invoke(fn); });

  return instanceInjector;

  ////////////////////////////////////
  // $provider
  ////////////////////////////////////

  function supportObject(delegate) {
    return function(key, value) {
      if (isObject(key)) {
        forEach(key, reverseParams(delegate));
      } else {
        return delegate(key, value);
      }
    };
  }

  function provider(name, provider_) {
    assertNotHasOwnProperty(name, 'service');
    if (isFunction(provider_) || isArray(provider_)) {
      provider_ = providerInjector.instantiate(provider_);
    }
    if (!provider_.$get) {
      throw $injectorMinErr('pget', 'Provider \'{0}\' must define $get factory method.', name);
    }
    return (providerCache[name + providerSuffix] = provider_);
  }

  function enforceReturnValue(name, factory) {
    return /** @this */ function enforcedReturnValue() {
      var result = instanceInjector.invoke(factory, this);
      if (isUndefined(result)) {
        throw $injectorMinErr('undef', 'Provider \'{0}\' must return a value from $get factory method.', name);
      }
      return result;
    };
  }

  function factory(name, factoryFn, enforce) {
    return provider(name, {
      $get: enforce !== false ? enforceReturnValue(name, factoryFn) : factoryFn
    });
  }

  function service(name, constructor) {
    return factory(name, ['$injector', function($injector) {
      return $injector.instantiate(constructor);
    }]);
  }

  function value(name, val) { return factory(name, valueFn(val), false); }

  function constant(name, value) {
    assertNotHasOwnProperty(name, 'constant');
    providerCache[name] = value;
    instanceCache[name] = value;
  }

  function decorator(serviceName, decorFn) {
    var origProvider = providerInjector.get(serviceName + providerSuffix),
        orig$get = origProvider.$get;

    origProvider.$get = function() {
      var origInstance = instanceInjector.invoke(orig$get, origProvider);
      return instanceInjector.invoke(decorFn, null, {$delegate: origInstance});
    };
  }

  ////////////////////////////////////
  // Module Loading
  ////////////////////////////////////
  function loadModules(modulesToLoad) {
    assertArg(isUndefined(modulesToLoad) || isArray(modulesToLoad), 'modulesToLoad', 'not an array');
    var runBlocks = [], moduleFn;
    forEach(modulesToLoad, function(module) {
      if (loadedModules.get(module)) return;
      loadedModules.set(module, true);

      function runInvokeQueue(queue) {
        var i, ii;
        for (i = 0, ii = queue.length; i < ii; i++) {
          var invokeArgs = queue[i],
              provider = providerInjector.get(invokeArgs[0]);

          provider[invokeArgs[1]].apply(provider, invokeArgs[2]);
        }
      }

      try {
        if (isString(module)) {
          moduleFn = angularModule(module);
          instanceInjector.modules[module] = moduleFn;
          runBlocks = runBlocks.concat(loadModules(moduleFn.requires)).concat(moduleFn._runBlocks);
          runInvokeQueue(moduleFn._invokeQueue);
          runInvokeQueue(moduleFn._configBlocks);
        } else if (isFunction(module)) {
            runBlocks.push(providerInjector.invoke(module));
        } else if (isArray(module)) {
            runBlocks.push(providerInjector.invoke(module));
        } else {
          assertArgFn(module, 'module');
        }
      } catch (e) {
        if (isArray(module)) {
          module = module[module.length - 1];
        }
        if (e.message && e.stack && e.stack.indexOf(e.message) === -1) {
          // Safari & FF's stack traces don't contain error.message content
          // unlike those of Chrome and IE
          // So if stack doesn't contain message, we create a new string that contains both.
          // Since error.stack is read-only in Safari, I'm overriding e and not e.stack here.
          // eslint-disable-next-line no-ex-assign
          e = e.message + '\n' + e.stack;
        }
        throw $injectorMinErr('modulerr', 'Failed to instantiate module {0} due to:\n{1}',
                  module, e.stack || e.message || e);
      }
    });
    return runBlocks;
  }

  ////////////////////////////////////
  // internal Injector
  ////////////////////////////////////

  function createInternalInjector(cache, factory) {

    function getService(serviceName, caller) {
      if (cache.hasOwnProperty(serviceName)) {
        if (cache[serviceName] === INSTANTIATING) {
          throw $injectorMinErr('cdep', 'Circular dependency found: {0}',
                    serviceName + ' <- ' + path.join(' <- '));
        }
        return cache[serviceName];
      } else {
        try {
          path.unshift(serviceName);
          cache[serviceName] = INSTANTIATING;
          cache[serviceName] = factory(serviceName, caller);
          return cache[serviceName];
        } catch (err) {
          if (cache[serviceName] === INSTANTIATING) {
            delete cache[serviceName];
          }
          throw err;
        } finally {
          path.shift();
        }
      }
    }


    function injectionArgs(fn, locals, serviceName) {
      var args = [],
          $inject = createInjector.$$annotate(fn, strictDi, serviceName);

      for (var i = 0, length = $inject.length; i < length; i++) {
        var key = $inject[i];
        if (typeof key !== 'string') {
          throw $injectorMinErr('itkn',
                  'Incorrect injection token! Expected service name as string, got {0}', key);
        }
        args.push(locals && locals.hasOwnProperty(key) ? locals[key] :
                                                         getService(key, serviceName));
      }
      return args;
    }

    function isClass(func) {
      // Support: IE 9-11 only
      // IE 9-11 do not support classes and IE9 leaks with the code below.
      if (msie || typeof func !== 'function') {
        return false;
      }
      var result = func.$$ngIsClass;
      if (!isBoolean(result)) {
        // Support: Edge 12-13 only
        // See: https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/6156135/
        result = func.$$ngIsClass = /^(?:class\b|constructor\()/.test(stringifyFn(func));
      }
      return result;
    }

    function invoke(fn, self, locals, serviceName) {
      if (typeof locals === 'string') {
        serviceName = locals;
        locals = null;
      }

      var args = injectionArgs(fn, locals, serviceName);
      if (isArray(fn)) {
        fn = fn[fn.length - 1];
      }

      if (!isClass(fn)) {
        // http://jsperf.com/angularjs-invoke-apply-vs-switch
        // #5388
        return fn.apply(self, args);
      } else {
        args.unshift(null);
        return new (Function.prototype.bind.apply(fn, args))();
      }
    }


    function instantiate(Type, locals, serviceName) {
      // Check if Type is annotated and use just the given function at n-1 as parameter
      // e.g. someModule.factory('greeter', ['$window', function(renamed$window) {}]);
      var ctor = (isArray(Type) ? Type[Type.length - 1] : Type);
      var args = injectionArgs(Type, locals, serviceName);
      // Empty object at position 0 is ignored for invocation with `new`, but required.
      args.unshift(null);
      return new (Function.prototype.bind.apply(ctor, args))();
    }


    return {
      invoke: invoke,
      instantiate: instantiate,
      get: getService,
      annotate: createInjector.$$annotate,
      has: function(name) {
        return providerCache.hasOwnProperty(name + providerSuffix) || cache.hasOwnProperty(name);
      }
    };
  }
}

createInjector.$$annotate = annotate;

/**
 * @ngdoc provider
 * @name $anchorScrollProvider
 * @this
 *
 * @description
 * Use `$anchorScrollProvider` to disable automatic scrolling whenever
 * {@link ng.$location#hash $location.hash()} changes.
 */
function $AnchorScrollProvider() {

  var autoScrollingEnabled = true;

  /**
   * @ngdoc method
   * @name $anchorScrollProvider#disableAutoScrolling
   *
   * @description
   * By default, {@link ng.$anchorScroll $anchorScroll()} will automatically detect changes to
   * {@link ng.$location#hash $location.hash()} and scroll to the element matching the new hash.<br />
   * Use this method to disable automatic scrolling.
   *
   * If automatic scrolling is disabled, one must explicitly call
   * {@link ng.$anchorScroll $anchorScroll()} in order to scroll to the element related to the
   * current hash.
   */
  this.disableAutoScrolling = function() {
    autoScrollingEnabled = false;
  };

  /**
   * @ngdoc service
   * @name $anchorScroll
   * @kind function
   * @requires $window
   * @requires $location
   * @requires $rootScope
   *
   * @description
   * When called, it scrolls to the element related to the specified `hash` or (if omitted) to the
   * current value of {@link ng.$location#hash $location.hash()}, according to the rules specified
   * in the
   * [HTML5 spec](http://www.w3.org/html/wg/drafts/html/master/browsers.html#an-indicated-part-of-the-document).
   *
   * It also watches the {@link ng.$location#hash $location.hash()} and automatically scrolls to
   * match any anchor whenever it changes. This can be disabled by calling
   * {@link ng.$anchorScrollProvider#disableAutoScrolling $anchorScrollProvider.disableAutoScrolling()}.
   *
   * Additionally, you can use its {@link ng.$anchorScroll#yOffset yOffset} property to specify a
   * vertical scroll-offset (either fixed or dynamic).
   *
   * @param {string=} hash The hash specifying the element to scroll to. If omitted, the value of
   *                       {@link ng.$location#hash $location.hash()} will be used.
   *
   * @property {(number|function|jqLite)} yOffset
   * If set, specifies a vertical scroll-offset. This is often useful when there are fixed
   * positioned elements at the top of the page, such as navbars, headers etc.
   *
   * `yOffset` can be specified in various ways:
   * - **number**: A fixed number of pixels to be used as offset.<br /><br />
   * - **function**: A getter function called everytime `$anchorScroll()` is executed. Must return
   *   a number representing the offset (in pixels).<br /><br />
   * - **jqLite**: A jqLite/jQuery element to be used for specifying the offset. The distance from
   *   the top of the page to the element's bottom will be used as offset.<br />
   *   **Note**: The element will be taken into account only as long as its `position` is set to
   *   `fixed`. This option is useful, when dealing with responsive navbars/headers that adjust
   *   their height and/or positioning according to the viewport's size.
   *
   * <br />
   * <div class="alert alert-warning">
   * In order for `yOffset` to work properly, scrolling should take place on the document's root and
   * not some child element.
   * </div>
   *
   * @example
     <example module="anchorScrollExample" name="anchor-scroll">
       <file name="index.html">
         <div id="scrollArea" ng-controller="ScrollController">
           <a ng-click="gotoBottom()">Go to bottom</a>
           <a id="bottom"></a> You're at the bottom!
         </div>
       </file>
       <file name="script.js">
         angular.module('anchorScrollExample', [])
           .controller('ScrollController', ['$scope', '$location', '$anchorScroll',
             function($scope, $location, $anchorScroll) {
               $scope.gotoBottom = function() {
                 // set the location.hash to the id of
                 // the element you wish to scroll to.
                 $location.hash('bottom');

                 // call $anchorScroll()
                 $anchorScroll();
               };
             }]);
       </file>
       <file name="style.css">
         #scrollArea {
           height: 280px;
           overflow: auto;
         }

         #bottom {
           display: block;
           margin-top: 2000px;
         }
       </file>
     </example>
   *
   * <hr />
   * The example below illustrates the use of a vertical scroll-offset (specified as a fixed value).
   * See {@link ng.$anchorScroll#yOffset $anchorScroll.yOffset} for more details.
   *
   * @example
     <example module="anchorScrollOffsetExample" name="anchor-scroll-offset">
       <file name="index.html">
         <div class="fixed-header" ng-controller="headerCtrl">
           <a href="" ng-click="gotoAnchor(x)" ng-repeat="x in [1,2,3,4,5]">
             Go to anchor {{x}}
           </a>
         </div>
         <div id="anchor{{x}}" class="anchor" ng-repeat="x in [1,2,3,4,5]">
           Anchor {{x}} of 5
         </div>
       </file>
       <file name="script.js">
         angular.module('anchorScrollOffsetExample', [])
           .run(['$anchorScroll', function($anchorScroll) {
             $anchorScroll.yOffset = 50;   // always scroll by 50 extra pixels
           }])
           .controller('headerCtrl', ['$anchorScroll', '$location', '$scope',
             function($anchorScroll, $location, $scope) {
               $scope.gotoAnchor = function(x) {
                 var newHash = 'anchor' + x;
                 if ($location.hash() !== newHash) {
                   // set the $location.hash to `newHash` and
                   // $anchorScroll will automatically scroll to it
                   $location.hash('anchor' + x);
                 } else {
                   // call $anchorScroll() explicitly,
                   // since $location.hash hasn't changed
                   $anchorScroll();
                 }
               };
             }
           ]);
       </file>
       <file name="style.css">
         body {
           padding-top: 50px;
         }

         .anchor {
           border: 2px dashed DarkOrchid;
           padding: 10px 10px 200px 10px;
         }

         .fixed-header {
           background-color: rgba(0, 0, 0, 0.2);
           height: 50px;
           position: fixed;
           top: 0; left: 0; right: 0;
         }

         .fixed-header > a {
           display: inline-block;
           margin: 5px 15px;
         }
       </file>
     </example>
   */
  this.$get = ['$window', '$location', '$rootScope', function($window, $location, $rootScope) {
    var document = $window.document;

    // Helper function to get first anchor from a NodeList
    // (using `Array#some()` instead of `angular#forEach()` since it's more performant
    //  and working in all supported browsers.)
    function getFirstAnchor(list) {
      var result = null;
      Array.prototype.some.call(list, function(element) {
        if (nodeName_(element) === 'a') {
          result = element;
          return true;
        }
      });
      return result;
    }

    function getYOffset() {

      var offset = scroll.yOffset;

      if (isFunction(offset)) {
        offset = offset();
      } else if (isElement(offset)) {
        var elem = offset[0];
        var style = $window.getComputedStyle(elem);
        if (style.position !== 'fixed') {
          offset = 0;
        } else {
          offset = elem.getBoundingClientRect().bottom;
        }
      } else if (!isNumber(offset)) {
        offset = 0;
      }

      return offset;
    }

    function scrollTo(elem) {
      if (elem) {
        elem.scrollIntoView();

        var offset = getYOffset();

        if (offset) {
          // `offset` is the number of pixels we should scroll UP in order to align `elem` properly.
          // This is true ONLY if the call to `elem.scrollIntoView()` initially aligns `elem` at the
          // top of the viewport.
          //
          // IF the number of pixels from the top of `elem` to the end of the page's content is less
          // than the height of the viewport, then `elem.scrollIntoView()` will align the `elem` some
          // way down the page.
          //
          // This is often the case for elements near the bottom of the page.
          //
          // In such cases we do not need to scroll the whole `offset` up, just the difference between
          // the top of the element and the offset, which is enough to align the top of `elem` at the
          // desired position.
          var elemTop = elem.getBoundingClientRect().top;
          $window.scrollBy(0, elemTop - offset);
        }
      } else {
        $window.scrollTo(0, 0);
      }
    }

    function scroll(hash) {
      // Allow numeric hashes
      hash = isString(hash) ? hash : isNumber(hash) ? hash.toString() : $location.hash();
      var elm;

      // empty hash, scroll to the top of the page
      if (!hash) scrollTo(null);

      // element with given id
      else if ((elm = document.getElementById(hash))) scrollTo(elm);

      // first anchor with given name :-D
      else if ((elm = getFirstAnchor(document.getElementsByName(hash)))) scrollTo(elm);

      // no element and hash === 'top', scroll to the top of the page
      else if (hash === 'top') scrollTo(null);
    }

    // does not scroll when user clicks on anchor link that is currently on
    // (no url change, no $location.hash() change), browser native does scroll
    if (autoScrollingEnabled) {
      $rootScope.$watch(function autoScrollWatch() {return $location.hash();},
        function autoScrollWatchAction(newVal, oldVal) {
          // skip the initial scroll if $location.hash is empty
          if (newVal === oldVal && newVal === '') return;

          jqLiteDocumentLoaded(function() {
            $rootScope.$evalAsync(scroll);
          });
        });
    }

    return scroll;
  }];
}

var $animateMinErr = minErr('$animate');
var ELEMENT_NODE = 1;
var NG_ANIMATE_CLASSNAME = 'ng-animate';

function mergeClasses(a,b) {
  if (!a && !b) return '';
  if (!a) return b;
  if (!b) return a;
  if (isArray(a)) a = a.join(' ');
  if (isArray(b)) b = b.join(' ');
  return a + ' ' + b;
}

function extractElementNode(element) {
  for (var i = 0; i < element.length; i++) {
    var elm = element[i];
    if (elm.nodeType === ELEMENT_NODE) {
      return elm;
    }
  }
}

function splitClasses(classes) {
  if (isString(classes)) {
    classes = classes.split(' ');
  }

  // Use createMap() to prevent class assumptions involving property names in
  // Object.prototype
  var obj = createMap();
  forEach(classes, function(klass) {
    // sometimes the split leaves empty string values
    // incase extra spaces were applied to the options
    if (klass.length) {
      obj[klass] = true;
    }
  });
  return obj;
}

// if any other type of options value besides an Object value is
// passed into the $animate.method() animation then this helper code
// will be run which will ignore it. While this patch is not the
// greatest solution to this, a lot of existing plugins depend on
// $animate to either call the callback (< 1.2) or return a promise
// that can be changed. This helper function ensures that the options
// are wiped clean incase a callback function is provided.
function prepareAnimateOptions(options) {
  return isObject(options)
      ? options
      : {};
}

var $$CoreAnimateJsProvider = /** @this */ function() {
  this.$get = noop;
};

// this is prefixed with Core since it conflicts with
// the animateQueueProvider defined in ngAnimate/animateQueue.js
var $$CoreAnimateQueueProvider = /** @this */ function() {
  var postDigestQueue = new NgMap();
  var postDigestElements = [];

  this.$get = ['$$AnimateRunner', '$rootScope',
       function($$AnimateRunner,   $rootScope) {
    return {
      enabled: noop,
      on: noop,
      off: noop,
      pin: noop,

      push: function(element, event, options, domOperation) {
        if (domOperation) {
          domOperation();
        }

        options = options || {};
        if (options.from) {
          element.css(options.from);
        }
        if (options.to) {
          element.css(options.to);
        }

        if (options.addClass || options.removeClass) {
          addRemoveClassesPostDigest(element, options.addClass, options.removeClass);
        }

        var runner = new $$AnimateRunner();

        // since there are no animations to run the runner needs to be
        // notified that the animation call is complete.
        runner.complete();
        return runner;
      }
    };


    function updateData(data, classes, value) {
      var changed = false;
      if (classes) {
        classes = isString(classes) ? classes.split(' ') :
                  isArray(classes) ? classes : [];
        forEach(classes, function(className) {
          if (className) {
            changed = true;
            data[className] = value;
          }
        });
      }
      return changed;
    }

    function handleCSSClassChanges() {
      forEach(postDigestElements, function(element) {
        var data = postDigestQueue.get(element);
        if (data) {
          var existing = splitClasses(element.attr('class'));
          var toAdd = '';
          var toRemove = '';
          forEach(data, function(status, className) {
            var hasClass = !!existing[className];
            if (status !== hasClass) {
              if (status) {
                toAdd += (toAdd.length ? ' ' : '') + className;
              } else {
                toRemove += (toRemove.length ? ' ' : '') + className;
              }
            }
          });

          forEach(element, function(elm) {
            if (toAdd) {
              jqLiteAddClass(elm, toAdd);
            }
            if (toRemove) {
              jqLiteRemoveClass(elm, toRemove);
            }
          });
          postDigestQueue.delete(element);
        }
      });
      postDigestElements.length = 0;
    }


    function addRemoveClassesPostDigest(element, add, remove) {
      var data = postDigestQueue.get(element) || {};

      var classesAdded = updateData(data, add, true);
      var classesRemoved = updateData(data, remove, false);

      if (classesAdded || classesRemoved) {

        postDigestQueue.set(element, data);
        postDigestElements.push(element);

        if (postDigestElements.length === 1) {
          $rootScope.$$postDigest(handleCSSClassChanges);
        }
      }
    }
  }];
};

/**
 * @ngdoc provider
 * @name $animateProvider
 *
 * @description
 * Default implementation of $animate that doesn't perform any animations, instead just
 * synchronously performs DOM updates and resolves the returned runner promise.
 *
 * In order to enable animations the `ngAnimate` module has to be loaded.
 *
 * To see the functional implementation check out `src/ngAnimate/animate.js`.
 */
var $AnimateProvider = ['$provide', /** @this */ function($provide) {
  var provider = this;
  var classNameFilter = null;

  this.$$registeredAnimations = Object.create(null);

   /**
   * @ngdoc method
   * @name $animateProvider#register
   *
   * @description
   * Registers a new injectable animation factory function. The factory function produces the
   * animation object which contains callback functions for each event that is expected to be
   * animated.
   *
   *   * `eventFn`: `function(element, ... , doneFunction, options)`
   *   The element to animate, the `doneFunction` and the options fed into the animation. Depending
   *   on the type of animation additional arguments will be injected into the animation function. The
   *   list below explains the function signatures for the different animation methods:
   *
   *   - setClass: function(element, addedClasses, removedClasses, doneFunction, options)
   *   - addClass: function(element, addedClasses, doneFunction, options)
   *   - removeClass: function(element, removedClasses, doneFunction, options)
   *   - enter, leave, move: function(element, doneFunction, options)
   *   - animate: function(element, fromStyles, toStyles, doneFunction, options)
   *
   *   Make sure to trigger the `doneFunction` once the animation is fully complete.
   *
   * ```js
   *   return {
   *     //enter, leave, move signature
   *     eventFn : function(element, done, options) {
   *       //code to run the animation
   *       //once complete, then run done()
   *       return function endFunction(wasCancelled) {
   *         //code to cancel the animation
   *       }
   *     }
   *   }
   * ```
   *
   * @param {string} name The name of the animation (this is what the class-based CSS value will be compared to).
   * @param {Function} factory The factory function that will be executed to return the animation
   *                           object.
   */
  this.register = function(name, factory) {
    if (name && name.charAt(0) !== '.') {
      throw $animateMinErr('notcsel', 'Expecting class selector starting with \'.\' got \'{0}\'.', name);
    }

    var key = name + '-animation';
    provider.$$registeredAnimations[name.substr(1)] = key;
    $provide.factory(key, factory);
  };

  /**
   * @ngdoc method
   * @name $animateProvider#classNameFilter
   *
   * @description
   * Sets and/or returns the CSS class regular expression that is checked when performing
   * an animation. Upon bootstrap the classNameFilter value is not set at all and will
   * therefore enable $animate to attempt to perform an animation on any element that is triggered.
   * When setting the `classNameFilter` value, animations will only be performed on elements
   * that successfully match the filter expression. This in turn can boost performance
   * for low-powered devices as well as applications containing a lot of structural operations.
   * @param {RegExp=} expression The className expression which will be checked against all animations
   * @return {RegExp} The current CSS className expression value. If null then there is no expression value
   */
  this.classNameFilter = function(expression) {
    if (arguments.length === 1) {
      classNameFilter = (expression instanceof RegExp) ? expression : null;
      if (classNameFilter) {
        var reservedRegex = new RegExp('[(\\s|\\/)]' + NG_ANIMATE_CLASSNAME + '[(\\s|\\/)]');
        if (reservedRegex.test(classNameFilter.toString())) {
          classNameFilter = null;
          throw $animateMinErr('nongcls', '$animateProvider.classNameFilter(regex) prohibits accepting a regex value which matches/contains the "{0}" CSS class.', NG_ANIMATE_CLASSNAME);
        }
      }
    }
    return classNameFilter;
  };

  this.$get = ['$$animateQueue', function($$animateQueue) {
    function domInsert(element, parentElement, afterElement) {
      // if for some reason the previous element was removed
      // from the dom sometime before this code runs then let's
      // just stick to using the parent element as the anchor
      if (afterElement) {
        var afterNode = extractElementNode(afterElement);
        if (afterNode && !afterNode.parentNode && !afterNode.previousElementSibling) {
          afterElement = null;
        }
      }
      if (afterElement) {
        afterElement.after(element);
      } else {
        parentElement.prepend(element);
      }
    }

    /**
     * @ngdoc service
     * @name $animate
     * @description The $animate service exposes a series of DOM utility methods that provide support
     * for animation hooks. The default behavior is the application of DOM operations, however,
     * when an animation is detected (and animations are enabled), $animate will do the heavy lifting
     * to ensure that animation runs with the triggered DOM operation.
     *
     * By default $animate doesn't trigger any animations. This is because the `ngAnimate` module isn't
     * included and only when it is active then the animation hooks that `$animate` triggers will be
     * functional. Once active then all structural `ng-` directives will trigger animations as they perform
     * their DOM-related operations (enter, leave and move). Other directives such as `ngClass`,
     * `ngShow`, `ngHide` and `ngMessages` also provide support for animations.
     *
     * It is recommended that the`$animate` service is always used when executing DOM-related procedures within directives.
     *
     * To learn more about enabling animation support, click here to visit the
     * {@link ngAnimate ngAnimate module page}.
     */
    return {
      // we don't call it directly since non-existant arguments may
      // be interpreted as null within the sub enabled function

      /**
       *
       * @ngdoc method
       * @name $animate#on
       * @kind function
       * @description Sets up an event listener to fire whenever the animation event (enter, leave, move, etc...)
       *    has fired on the given element or among any of its children. Once the listener is fired, the provided callback
       *    is fired with the following params:
       *
       * ```js
       * $animate.on('enter', container,
       *    function callback(element, phase) {
       *      // cool we detected an enter animation within the container
       *    }
       * );
       * ```
       *
       * @param {string} event the animation event that will be captured (e.g. enter, leave, move, addClass, removeClass, etc...)
       * @param {DOMElement} container the container element that will capture each of the animation events that are fired on itself
       *     as well as among its children
       * @param {Function} callback the callback function that will be fired when the listener is triggered
       *
       * The arguments present in the callback function are:
       * * `element` - The captured DOM element that the animation was fired on.
       * * `phase` - The phase of the animation. The two possible phases are **start** (when the animation starts) and **close** (when it ends).
       */
      on: $$animateQueue.on,

      /**
       *
       * @ngdoc method
       * @name $animate#off
       * @kind function
       * @description Deregisters an event listener based on the event which has been associated with the provided element. This method
       * can be used in three different ways depending on the arguments:
       *
       * ```js
       * // remove all the animation event listeners listening for `enter`
       * $animate.off('enter');
       *
       * // remove listeners for all animation events from the container element
       * $animate.off(container);
       *
       * // remove all the animation event listeners listening for `enter` on the given element and its children
       * $animate.off('enter', container);
       *
       * // remove the event listener function provided by `callback` that is set
       * // to listen for `enter` on the given `container` as well as its children
       * $animate.off('enter', container, callback);
       * ```
       *
       * @param {string|DOMElement} event|container the animation event (e.g. enter, leave, move,
       * addClass, removeClass, etc...), or the container element. If it is the element, all other
       * arguments are ignored.
       * @param {DOMElement=} container the container element the event listener was placed on
       * @param {Function=} callback the callback function that was registered as the listener
       */
      off: $$animateQueue.off,

      /**
       * @ngdoc method
       * @name $animate#pin
       * @kind function
       * @description Associates the provided element with a host parent element to allow the element to be animated even if it exists
       *    outside of the DOM structure of the Angular application. By doing so, any animation triggered via `$animate` can be issued on the
       *    element despite being outside the realm of the application or within another application. Say for example if the application
       *    was bootstrapped on an element that is somewhere inside of the `<body>` tag, but we wanted to allow for an element to be situated
       *    as a direct child of `document.body`, then this can be achieved by pinning the element via `$animate.pin(element)`. Keep in mind
       *    that calling `$animate.pin(element, parentElement)` will not actually insert into the DOM anywhere; it will just create the association.
       *
       *    Note that this feature is only active when the `ngAnimate` module is used.
       *
       * @param {DOMElement} element the external element that will be pinned
       * @param {DOMElement} parentElement the host parent element that will be associated with the external element
       */
      pin: $$animateQueue.pin,

      /**
       *
       * @ngdoc method
       * @name $animate#enabled
       * @kind function
       * @description Used to get and set whether animations are enabled or not on the entire application or on an element and its children. This
       * function can be called in four ways:
       *
       * ```js
       * // returns true or false
       * $animate.enabled();
       *
       * // changes the enabled state for all animations
       * $animate.enabled(false);
       * $animate.enabled(true);
       *
       * // returns true or false if animations are enabled for an element
       * $animate.enabled(element);
       *
       * // changes the enabled state for an element and its children
       * $animate.enabled(element, true);
       * $animate.enabled(element, false);
       * ```
       *
       * @param {DOMElement=} element the element that will be considered for checking/setting the enabled state
       * @param {boolean=} enabled whether or not the animations will be enabled for the element
       *
       * @return {boolean} whether or not animations are enabled
       */
      enabled: $$animateQueue.enabled,

      /**
       * @ngdoc method
       * @name $animate#cancel
       * @kind function
       * @description Cancels the provided animation.
       *
       * @param {Promise} animationPromise The animation promise that is returned when an animation is started.
       */
      cancel: function(runner) {
        if (runner.end) {
          runner.end();
        }
      },

      /**
       *
       * @ngdoc method
       * @name $animate#enter
       * @kind function
       * @description Inserts the element into the DOM either after the `after` element (if provided) or
       *   as the first child within the `parent` element and then triggers an animation.
       *   A promise is returned that will be resolved during the next digest once the animation
       *   has completed.
       *
       * @param {DOMElement} element the element which will be inserted into the DOM
       * @param {DOMElement} parent the parent element which will append the element as
       *   a child (so long as the after element is not present)
       * @param {DOMElement=} after the sibling element after which the element will be appended
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      enter: function(element, parent, after, options) {
        parent = parent && jqLite(parent);
        after = after && jqLite(after);
        parent = parent || after.parent();
        domInsert(element, parent, after);
        return $$animateQueue.push(element, 'enter', prepareAnimateOptions(options));
      },

      /**
       *
       * @ngdoc method
       * @name $animate#move
       * @kind function
       * @description Inserts (moves) the element into its new position in the DOM either after
       *   the `after` element (if provided) or as the first child within the `parent` element
       *   and then triggers an animation. A promise is returned that will be resolved
       *   during the next digest once the animation has completed.
       *
       * @param {DOMElement} element the element which will be moved into the new DOM position
       * @param {DOMElement} parent the parent element which will append the element as
       *   a child (so long as the after element is not present)
       * @param {DOMElement=} after the sibling element after which the element will be appended
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      move: function(element, parent, after, options) {
        parent = parent && jqLite(parent);
        after = after && jqLite(after);
        parent = parent || after.parent();
        domInsert(element, parent, after);
        return $$animateQueue.push(element, 'move', prepareAnimateOptions(options));
      },

      /**
       * @ngdoc method
       * @name $animate#leave
       * @kind function
       * @description Triggers an animation and then removes the element from the DOM.
       * When the function is called a promise is returned that will be resolved during the next
       * digest once the animation has completed.
       *
       * @param {DOMElement} element the element which will be removed from the DOM
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      leave: function(element, options) {
        return $$animateQueue.push(element, 'leave', prepareAnimateOptions(options), function() {
          element.remove();
        });
      },

      /**
       * @ngdoc method
       * @name $animate#addClass
       * @kind function
       *
       * @description Triggers an addClass animation surrounding the addition of the provided CSS class(es). Upon
       *   execution, the addClass operation will only be handled after the next digest and it will not trigger an
       *   animation if element already contains the CSS class or if the class is removed at a later step.
       *   Note that class-based animations are treated differently compared to structural animations
       *   (like enter, move and leave) since the CSS classes may be added/removed at different points
       *   depending if CSS or JavaScript animations are used.
       *
       * @param {DOMElement} element the element which the CSS classes will be applied to
       * @param {string} className the CSS class(es) that will be added (multiple classes are separated via spaces)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      addClass: function(element, className, options) {
        options = prepareAnimateOptions(options);
        options.addClass = mergeClasses(options.addclass, className);
        return $$animateQueue.push(element, 'addClass', options);
      },

      /**
       * @ngdoc method
       * @name $animate#removeClass
       * @kind function
       *
       * @description Triggers a removeClass animation surrounding the removal of the provided CSS class(es). Upon
       *   execution, the removeClass operation will only be handled after the next digest and it will not trigger an
       *   animation if element does not contain the CSS class or if the class is added at a later step.
       *   Note that class-based animations are treated differently compared to structural animations
       *   (like enter, move and leave) since the CSS classes may be added/removed at different points
       *   depending if CSS or JavaScript animations are used.
       *
       * @param {DOMElement} element the element which the CSS classes will be applied to
       * @param {string} className the CSS class(es) that will be removed (multiple classes are separated via spaces)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      removeClass: function(element, className, options) {
        options = prepareAnimateOptions(options);
        options.removeClass = mergeClasses(options.removeClass, className);
        return $$animateQueue.push(element, 'removeClass', options);
      },

      /**
       * @ngdoc method
       * @name $animate#setClass
       * @kind function
       *
       * @description Performs both the addition and removal of a CSS classes on an element and (during the process)
       *    triggers an animation surrounding the class addition/removal. Much like `$animate.addClass` and
       *    `$animate.removeClass`, `setClass` will only evaluate the classes being added/removed once a digest has
       *    passed. Note that class-based animations are treated differently compared to structural animations
       *    (like enter, move and leave) since the CSS classes may be added/removed at different points
       *    depending if CSS or JavaScript animations are used.
       *
       * @param {DOMElement} element the element which the CSS classes will be applied to
       * @param {string} add the CSS class(es) that will be added (multiple classes are separated via spaces)
       * @param {string} remove the CSS class(es) that will be removed (multiple classes are separated via spaces)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      setClass: function(element, add, remove, options) {
        options = prepareAnimateOptions(options);
        options.addClass = mergeClasses(options.addClass, add);
        options.removeClass = mergeClasses(options.removeClass, remove);
        return $$animateQueue.push(element, 'setClass', options);
      },

      /**
       * @ngdoc method
       * @name $animate#animate
       * @kind function
       *
       * @description Performs an inline animation on the element which applies the provided to and from CSS styles to the element.
       * If any detected CSS transition, keyframe or JavaScript matches the provided className value, then the animation will take
       * on the provided styles. For example, if a transition animation is set for the given className, then the provided `from` and
       * `to` styles will be applied alongside the given transition. If the CSS style provided in `from` does not have a corresponding
       * style in `to`, the style in `from` is applied immediately, and no animation is run.
       * If a JavaScript animation is detected then the provided styles will be given in as function parameters into the `animate`
       * method (or as part of the `options` parameter):
       *
       * ```js
       * ngModule.animation('.my-inline-animation', function() {
       *   return {
       *     animate : function(element, from, to, done, options) {
       *       //animation
       *       done();
       *     }
       *   }
       * });
       * ```
       *
       * @param {DOMElement} element the element which the CSS styles will be applied to
       * @param {object} from the from (starting) CSS styles that will be applied to the element and across the animation.
       * @param {object} to the to (destination) CSS styles that will be applied to the element and across the animation.
       * @param {string=} className an optional CSS class that will be applied to the element for the duration of the animation. If
       *    this value is left as empty then a CSS class of `ng-inline-animate` will be applied to the element.
       *    (Note that if no animation is detected then this value will not be applied to the element.)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      animate: function(element, from, to, className, options) {
        options = prepareAnimateOptions(options);
        options.from = options.from ? extend(options.from, from) : from;
        options.to   = options.to   ? extend(options.to, to)     : to;

        className = className || 'ng-inline-animate';
        options.tempClasses = mergeClasses(options.tempClasses, className);
        return $$animateQueue.push(element, 'animate', options);
      }
    };
  }];
}];

var $$AnimateAsyncRunFactoryProvider = /** @this */ function() {
  this.$get = ['$$rAF', function($$rAF) {
    var waitQueue = [];

    function waitForTick(fn) {
      waitQueue.push(fn);
      if (waitQueue.length > 1) return;
      $$rAF(function() {
        for (var i = 0; i < waitQueue.length; i++) {
          waitQueue[i]();
        }
        waitQueue = [];
      });
    }

    return function() {
      var passed = false;
      waitForTick(function() {
        passed = true;
      });
      return function(callback) {
        if (passed) {
          callback();
        } else {
          waitForTick(callback);
        }
      };
    };
  }];
};

var $$AnimateRunnerFactoryProvider = /** @this */ function() {
  this.$get = ['$q', '$sniffer', '$$animateAsyncRun', '$$isDocumentHidden', '$timeout',
       function($q,   $sniffer,   $$animateAsyncRun,   $$isDocumentHidden,   $timeout) {

    var INITIAL_STATE = 0;
    var DONE_PENDING_STATE = 1;
    var DONE_COMPLETE_STATE = 2;

    AnimateRunner.chain = function(chain, callback) {
      var index = 0;

      next();
      function next() {
        if (index === chain.length) {
          callback(true);
          return;
        }

        chain[index](function(response) {
          if (response === false) {
            callback(false);
            return;
          }
          index++;
          next();
        });
      }
    };

    AnimateRunner.all = function(runners, callback) {
      var count = 0;
      var status = true;
      forEach(runners, function(runner) {
        runner.done(onProgress);
      });

      function onProgress(response) {
        status = status && response;
        if (++count === runners.length) {
          callback(status);
        }
      }
    };

    function AnimateRunner(host) {
      this.setHost(host);

      var rafTick = $$animateAsyncRun();
      var timeoutTick = function(fn) {
        $timeout(fn, 0, false);
      };

      this._doneCallbacks = [];
      this._tick = function(fn) {
        if ($$isDocumentHidden()) {
          timeoutTick(fn);
        } else {
          rafTick(fn);
        }
      };
      this._state = 0;
    }

    AnimateRunner.prototype = {
      setHost: function(host) {
        this.host = host || {};
      },

      done: function(fn) {
        if (this._state === DONE_COMPLETE_STATE) {
          fn();
        } else {
          this._doneCallbacks.push(fn);
        }
      },

      progress: noop,

      getPromise: function() {
        if (!this.promise) {
          var self = this;
          this.promise = $q(function(resolve, reject) {
            self.done(function(status) {
              if (status === false) {
                reject();
              } else {
                resolve();
              }
            });
          });
        }
        return this.promise;
      },

      then: function(resolveHandler, rejectHandler) {
        return this.getPromise().then(resolveHandler, rejectHandler);
      },

      'catch': function(handler) {
        return this.getPromise()['catch'](handler);
      },

      'finally': function(handler) {
        return this.getPromise()['finally'](handler);
      },

      pause: function() {
        if (this.host.pause) {
          this.host.pause();
        }
      },

      resume: function() {
        if (this.host.resume) {
          this.host.resume();
        }
      },

      end: function() {
        if (this.host.end) {
          this.host.end();
        }
        this._resolve(true);
      },

      cancel: function() {
        if (this.host.cancel) {
          this.host.cancel();
        }
        this._resolve(false);
      },

      complete: function(response) {
        var self = this;
        if (self._state === INITIAL_STATE) {
          self._state = DONE_PENDING_STATE;
          self._tick(function() {
            self._resolve(response);
          });
        }
      },

      _resolve: function(response) {
        if (this._state !== DONE_COMPLETE_STATE) {
          forEach(this._doneCallbacks, function(fn) {
            fn(response);
          });
          this._doneCallbacks.length = 0;
          this._state = DONE_COMPLETE_STATE;
        }
      }
    };

    return AnimateRunner;
  }];
};

/* exported $CoreAnimateCssProvider */

/**
 * @ngdoc service
 * @name $animateCss
 * @kind object
 * @this
 *
 * @description
 * This is the core version of `$animateCss`. By default, only when the `ngAnimate` is included,
 * then the `$animateCss` service will actually perform animations.
 *
 * Click here {@link ngAnimate.$animateCss to read the documentation for $animateCss}.
 */
var $CoreAnimateCssProvider = function() {
  this.$get = ['$$rAF', '$q', '$$AnimateRunner', function($$rAF, $q, $$AnimateRunner) {

    return function(element, initialOptions) {
      // all of the animation functions should create
      // a copy of the options data, however, if a
      // parent service has already created a copy then
      // we should stick to using that
      var options = initialOptions || {};
      if (!options.$$prepared) {
        options = copy(options);
      }

      // there is no point in applying the styles since
      // there is no animation that goes on at all in
      // this version of $animateCss.
      if (options.cleanupStyles) {
        options.from = options.to = null;
      }

      if (options.from) {
        element.css(options.from);
        options.from = null;
      }

      var closed, runner = new $$AnimateRunner();
      return {
        start: run,
        end: run
      };

      function run() {
        $$rAF(function() {
          applyAnimationContents();
          if (!closed) {
            runner.complete();
          }
          closed = true;
        });
        return runner;
      }

      function applyAnimationContents() {
        if (options.addClass) {
          element.addClass(options.addClass);
          options.addClass = null;
        }
        if (options.removeClass) {
          element.removeClass(options.removeClass);
          options.removeClass = null;
        }
        if (options.to) {
          element.css(options.to);
          options.to = null;
        }
      }
    };
  }];
};

/* global stripHash: true */

/**
 * ! This is a private undocumented service !
 *
 * @name $browser
 * @requires $log
 * @description
 * This object has two goals:
 *
 * - hide all the global state in the browser caused by the window object
 * - abstract away all the browser specific features and inconsistencies
 *
 * For tests we provide {@link ngMock.$browser mock implementation} of the `$browser`
 * service, which can be used for convenient testing of the application without the interaction with
 * the real browser apis.
 */
/**
 * @param {object} window The global window object.
 * @param {object} document jQuery wrapped document.
 * @param {object} $log window.console or an object with the same interface.
 * @param {object} $sniffer $sniffer service
 */
function Browser(window, document, $log, $sniffer) {
  var self = this,
      location = window.location,
      history = window.history,
      setTimeout = window.setTimeout,
      clearTimeout = window.clearTimeout,
      pendingDeferIds = {};

  self.isMock = false;

  var outstandingRequestCount = 0;
  var outstandingRequestCallbacks = [];

  // TODO(vojta): remove this temporary api
  self.$$completeOutstandingRequest = completeOutstandingRequest;
  self.$$incOutstandingRequestCount = function() { outstandingRequestCount++; };

  /**
   * Executes the `fn` function(supports currying) and decrements the `outstandingRequestCallbacks`
   * counter. If the counter reaches 0, all the `outstandingRequestCallbacks` are executed.
   */
  function completeOutstandingRequest(fn) {
    try {
      fn.apply(null, sliceArgs(arguments, 1));
    } finally {
      outstandingRequestCount--;
      if (outstandingRequestCount === 0) {
        while (outstandingRequestCallbacks.length) {
          try {
            outstandingRequestCallbacks.pop()();
          } catch (e) {
            $log.error(e);
          }
        }
      }
    }
  }

  function getHash(url) {
    var index = url.indexOf('#');
    return index === -1 ? '' : url.substr(index);
  }

  /**
   * @private
   * Note: this method is used only by scenario runner
   * TODO(vojta): prefix this method with $$ ?
   * @param {function()} callback Function that will be called when no outstanding request
   */
  self.notifyWhenNoOutstandingRequests = function(callback) {
    if (outstandingRequestCount === 0) {
      callback();
    } else {
      outstandingRequestCallbacks.push(callback);
    }
  };

  //////////////////////////////////////////////////////////////
  // URL API
  //////////////////////////////////////////////////////////////

  var cachedState, lastHistoryState,
      lastBrowserUrl = location.href,
      baseElement = document.find('base'),
      pendingLocation = null,
      getCurrentState = !$sniffer.history ? noop : function getCurrentState() {
        try {
          return history.state;
        } catch (e) {
          // MSIE can reportedly throw when there is no state (UNCONFIRMED).
        }
      };

  cacheState();

  /**
   * @name $browser#url
   *
   * @description
   * GETTER:
   * Without any argument, this method just returns current value of location.href.
   *
   * SETTER:
   * With at least one argument, this method sets url to new value.
   * If html5 history api supported, pushState/replaceState is used, otherwise
   * location.href/location.replace is used.
   * Returns its own instance to allow chaining
   *
   * NOTE: this api is intended for use only by the $location service. Please use the
   * {@link ng.$location $location service} to change url.
   *
   * @param {string} url New url (when used as setter)
   * @param {boolean=} replace Should new url replace current history record?
   * @param {object=} state object to use with pushState/replaceState
   */
  self.url = function(url, replace, state) {
    // In modern browsers `history.state` is `null` by default; treating it separately
    // from `undefined` would cause `$browser.url('/foo')` to change `history.state`
    // to undefined via `pushState`. Instead, let's change `undefined` to `null` here.
    if (isUndefined(state)) {
      state = null;
    }

    // Android Browser BFCache causes location, history reference to become stale.
    if (location !== window.location) location = window.location;
    if (history !== window.history) history = window.history;

    // setter
    if (url) {
      var sameState = lastHistoryState === state;

      // Don't change anything if previous and current URLs and states match. This also prevents
      // IE<10 from getting into redirect loop when in LocationHashbangInHtml5Url mode.
      // See https://github.com/angular/angular.js/commit/ffb2701
      if (lastBrowserUrl === url && (!$sniffer.history || sameState)) {
        return self;
      }
      var sameBase = lastBrowserUrl && stripHash(lastBrowserUrl) === stripHash(url);
      lastBrowserUrl = url;
      lastHistoryState = state;
      // Don't use history API if only the hash changed
      // due to a bug in IE10/IE11 which leads
      // to not firing a `hashchange` nor `popstate` event
      // in some cases (see #9143).
      if ($sniffer.history && (!sameBase || !sameState)) {
        history[replace ? 'replaceState' : 'pushState'](state, '', url);
        cacheState();
      } else {
        if (!sameBase) {
          pendingLocation = url;
        }
        if (replace) {
          location.replace(url);
        } else if (!sameBase) {
          location.href = url;
        } else {
          location.hash = getHash(url);
        }
        if (location.href !== url) {
          pendingLocation = url;
        }
      }
      if (pendingLocation) {
        pendingLocation = url;
      }
      return self;
    // getter
    } else {
      // - pendingLocation is needed as browsers don't allow to read out
      //   the new location.href if a reload happened or if there is a bug like in iOS 9 (see
      //   https://openradar.appspot.com/22186109).
      // - the replacement is a workaround for https://bugzilla.mozilla.org/show_bug.cgi?id=407172
      return pendingLocation || location.href.replace(/%27/g,'\'');
    }
  };

  /**
   * @name $browser#state
   *
   * @description
   * This method is a getter.
   *
   * Return history.state or null if history.state is undefined.
   *
   * @returns {object} state
   */
  self.state = function() {
    return cachedState;
  };

  var urlChangeListeners = [],
      urlChangeInit = false;

  function cacheStateAndFireUrlChange() {
    pendingLocation = null;
    fireStateOrUrlChange();
  }

  // This variable should be used *only* inside the cacheState function.
  var lastCachedState = null;
  function cacheState() {
    // This should be the only place in $browser where `history.state` is read.
    cachedState = getCurrentState();
    cachedState = isUndefined(cachedState) ? null : cachedState;

    // Prevent callbacks fo fire twice if both hashchange & popstate were fired.
    if (equals(cachedState, lastCachedState)) {
      cachedState = lastCachedState;
    }

    lastCachedState = cachedState;
    lastHistoryState = cachedState;
  }

  function fireStateOrUrlChange() {
    var prevLastHistoryState = lastHistoryState;
    cacheState();

    if (lastBrowserUrl === self.url() && prevLastHistoryState === cachedState) {
      return;
    }

    lastBrowserUrl = self.url();
    lastHistoryState = cachedState;
    forEach(urlChangeListeners, function(listener) {
      listener(self.url(), cachedState);
    });
  }

  /**
   * @name $browser#onUrlChange
   *
   * @description
   * Register callback function that will be called, when url changes.
   *
   * It's only called when the url is changed from outside of angular:
   * - user types different url into address bar
   * - user clicks on history (forward/back) button
   * - user clicks on a link
   *
   * It's not called when url is changed by $browser.url() method
   *
   * The listener gets called with new url as parameter.
   *
   * NOTE: this api is intended for use only by the $location service. Please use the
   * {@link ng.$location $location service} to monitor url changes in angular apps.
   *
   * @param {function(string)} listener Listener function to be called when url changes.
   * @return {function(string)} Returns the registered listener fn - handy if the fn is anonymous.
   */
  self.onUrlChange = function(callback) {
    // TODO(vojta): refactor to use node's syntax for events
    if (!urlChangeInit) {
      // We listen on both (hashchange/popstate) when available, as some browsers don't
      // fire popstate when user changes the address bar and don't fire hashchange when url
      // changed by push/replaceState

      // html5 history api - popstate event
      if ($sniffer.history) jqLite(window).on('popstate', cacheStateAndFireUrlChange);
      // hashchange event
      jqLite(window).on('hashchange', cacheStateAndFireUrlChange);

      urlChangeInit = true;
    }

    urlChangeListeners.push(callback);
    return callback;
  };

  /**
   * @private
   * Remove popstate and hashchange handler from window.
   *
   * NOTE: this api is intended for use only by $rootScope.
   */
  self.$$applicationDestroyed = function() {
    jqLite(window).off('hashchange popstate', cacheStateAndFireUrlChange);
  };

  /**
   * Checks whether the url has changed outside of Angular.
   * Needs to be exported to be able to check for changes that have been done in sync,
   * as hashchange/popstate events fire in async.
   */
  self.$$checkUrlChange = fireStateOrUrlChange;

  //////////////////////////////////////////////////////////////
  // Misc API
  //////////////////////////////////////////////////////////////

  /**
   * @name $browser#baseHref
   *
   * @description
   * Returns current <base href>
   * (always relative - without domain)
   *
   * @returns {string} The current base href
   */
  self.baseHref = function() {
    var href = baseElement.attr('href');
    return href ? href.replace(/^(https?:)?\/\/[^/]*/, '') : '';
  };

  /**
   * @name $browser#defer
   * @param {function()} fn A function, who's execution should be deferred.
   * @param {number=} [delay=0] of milliseconds to defer the function execution.
   * @returns {*} DeferId that can be used to cancel the task via `$browser.defer.cancel()`.
   *
   * @description
   * Executes a fn asynchronously via `setTimeout(fn, delay)`.
   *
   * Unlike when calling `setTimeout` directly, in test this function is mocked and instead of using
   * `setTimeout` in tests, the fns are queued in an array, which can be programmatically flushed
   * via `$browser.defer.flush()`.
   *
   */
  self.defer = function(fn, delay) {
    var timeoutId;
    outstandingRequestCount++;
    timeoutId = setTimeout(function() {
      delete pendingDeferIds[timeoutId];
      completeOutstandingRequest(fn);
    }, delay || 0);
    pendingDeferIds[timeoutId] = true;
    return timeoutId;
  };


  /**
   * @name $browser#defer.cancel
   *
   * @description
   * Cancels a deferred task identified with `deferId`.
   *
   * @param {*} deferId Token returned by the `$browser.defer` function.
   * @returns {boolean} Returns `true` if the task hasn't executed yet and was successfully
   *                    canceled.
   */
  self.defer.cancel = function(deferId) {
    if (pendingDeferIds[deferId]) {
      delete pendingDeferIds[deferId];
      clearTimeout(deferId);
      completeOutstandingRequest(noop);
      return true;
    }
    return false;
  };

}

/** @this */
function $BrowserProvider() {
  this.$get = ['$window', '$log', '$sniffer', '$document',
      function($window, $log, $sniffer, $document) {
        return new Browser($window, $document, $log, $sniffer);
      }];
}

/**
 * @ngdoc service
 * @name $cacheFactory
 * @this
 *
 * @description
 * Factory that constructs {@link $cacheFactory.Cache Cache} objects and gives access to
 * them.
 *
 * ```js
 *
 *  var cache = $cacheFactory('cacheId');
 *  expect($cacheFactory.get('cacheId')).toBe(cache);
 *  expect($cacheFactory.get('noSuchCacheId')).not.toBeDefined();
 *
 *  cache.put("key", "value");
 *  cache.put("another key", "another value");
 *
 *  // We've specified no options on creation
 *  expect(cache.info()).toEqual({id: 'cacheId', size: 2});
 *
 * ```
 *
 *
 * @param {string} cacheId Name or id of the newly created cache.
 * @param {object=} options Options object that specifies the cache behavior. Properties:
 *
 *   - `{number=}` `capacity` — turns the cache into LRU cache.
 *
 * @returns {object} Newly created cache object with the following set of methods:
 *
 * - `{object}` `info()` — Returns id, size, and options of cache.
 * - `{{*}}` `put({string} key, {*} value)` — Puts a new key-value pair into the cache and returns
 *   it.
 * - `{{*}}` `get({string} key)` — Returns cached value for `key` or undefined for cache miss.
 * - `{void}` `remove({string} key)` — Removes a key-value pair from the cache.
 * - `{void}` `removeAll()` — Removes all cached values.
 * - `{void}` `destroy()` — Removes references to this cache from $cacheFactory.
 *
 * @example
   <example module="cacheExampleApp" name="cache-factory">
     <file name="index.html">
       <div ng-controller="CacheController">
         <input ng-model="newCacheKey" placeholder="Key">
         <input ng-model="newCacheValue" placeholder="Value">
         <button ng-click="put(newCacheKey, newCacheValue)">Cache</button>

         <p ng-if="keys.length">Cached Values</p>
         <div ng-repeat="key in keys">
           <span ng-bind="key"></span>
           <span>: </span>
           <b ng-bind="cache.get(key)"></b>
         </div>

         <p>Cache Info</p>
         <div ng-repeat="(key, value) in cache.info()">
           <span ng-bind="key"></span>
           <span>: </span>
           <b ng-bind="value"></b>
         </div>
       </div>
     </file>
     <file name="script.js">
       angular.module('cacheExampleApp', []).
         controller('CacheController', ['$scope', '$cacheFactory', function($scope, $cacheFactory) {
           $scope.keys = [];
           $scope.cache = $cacheFactory('cacheId');
           $scope.put = function(key, value) {
             if (angular.isUndefined($scope.cache.get(key))) {
               $scope.keys.push(key);
             }
             $scope.cache.put(key, angular.isUndefined(value) ? null : value);
           };
         }]);
     </file>
     <file name="style.css">
       p {
         margin: 10px 0 3px;
       }
     </file>
   </example>
 */
function $CacheFactoryProvider() {

  this.$get = function() {
    var caches = {};

    function cacheFactory(cacheId, options) {
      if (cacheId in caches) {
        throw minErr('$cacheFactory')('iid', 'CacheId \'{0}\' is already taken!', cacheId);
      }

      var size = 0,
          stats = extend({}, options, {id: cacheId}),
          data = createMap(),
          capacity = (options && options.capacity) || Number.MAX_VALUE,
          lruHash = createMap(),
          freshEnd = null,
          staleEnd = null;

      /**
       * @ngdoc type
       * @name $cacheFactory.Cache
       *
       * @description
       * A cache object used to store and retrieve data, primarily used by
       * {@link $http $http} and the {@link ng.directive:script script} directive to cache
       * templates and other data.
       *
       * ```js
       *  angular.module('superCache')
       *    .factory('superCache', ['$cacheFactory', function($cacheFactory) {
       *      return $cacheFactory('super-cache');
       *    }]);
       * ```
       *
       * Example test:
       *
       * ```js
       *  it('should behave like a cache', inject(function(superCache) {
       *    superCache.put('key', 'value');
       *    superCache.put('another key', 'another value');
       *
       *    expect(superCache.info()).toEqual({
       *      id: 'super-cache',
       *      size: 2
       *    });
       *
       *    superCache.remove('another key');
       *    expect(superCache.get('another key')).toBeUndefined();
       *
       *    superCache.removeAll();
       *    expect(superCache.info()).toEqual({
       *      id: 'super-cache',
       *      size: 0
       *    });
       *  }));
       * ```
       */
      return (caches[cacheId] = {

        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#put
         * @kind function
         *
         * @description
         * Inserts a named entry into the {@link $cacheFactory.Cache Cache} object to be
         * retrieved later, and incrementing the size of the cache if the key was not already
         * present in the cache. If behaving like an LRU cache, it will also remove stale
         * entries from the set.
         *
         * It will not insert undefined values into the cache.
         *
         * @param {string} key the key under which the cached data is stored.
         * @param {*} value the value to store alongside the key. If it is undefined, the key
         *    will not be stored.
         * @returns {*} the value stored.
         */
        put: function(key, value) {
          if (isUndefined(value)) return;
          if (capacity < Number.MAX_VALUE) {
            var lruEntry = lruHash[key] || (lruHash[key] = {key: key});

            refresh(lruEntry);
          }

          if (!(key in data)) size++;
          data[key] = value;

          if (size > capacity) {
            this.remove(staleEnd.key);
          }

          return value;
        },

        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#get
         * @kind function
         *
         * @description
         * Retrieves named data stored in the {@link $cacheFactory.Cache Cache} object.
         *
         * @param {string} key the key of the data to be retrieved
         * @returns {*} the value stored.
         */
        get: function(key) {
          if (capacity < Number.MAX_VALUE) {
            var lruEntry = lruHash[key];

            if (!lruEntry) return;

            refresh(lruEntry);
          }

          return data[key];
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#remove
         * @kind function
         *
         * @description
         * Removes an entry from the {@link $cacheFactory.Cache Cache} object.
         *
         * @param {string} key the key of the entry to be removed
         */
        remove: function(key) {
          if (capacity < Number.MAX_VALUE) {
            var lruEntry = lruHash[key];

            if (!lruEntry) return;

            if (lruEntry === freshEnd) freshEnd = lruEntry.p;
            if (lruEntry === staleEnd) staleEnd = lruEntry.n;
            link(lruEntry.n,lruEntry.p);

            delete lruHash[key];
          }

          if (!(key in data)) return;

          delete data[key];
          size--;
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#removeAll
         * @kind function
         *
         * @description
         * Clears the cache object of any entries.
         */
        removeAll: function() {
          data = createMap();
          size = 0;
          lruHash = createMap();
          freshEnd = staleEnd = null;
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#destroy
         * @kind function
         *
         * @description
         * Destroys the {@link $cacheFactory.Cache Cache} object entirely,
         * removing it from the {@link $cacheFactory $cacheFactory} set.
         */
        destroy: function() {
          data = null;
          stats = null;
          lruHash = null;
          delete caches[cacheId];
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#info
         * @kind function
         *
         * @description
         * Retrieve information regarding a particular {@link $cacheFactory.Cache Cache}.
         *
         * @returns {object} an object with the following properties:
         *   <ul>
         *     <li>**id**: the id of the cache instance</li>
         *     <li>**size**: the number of entries kept in the cache instance</li>
         *     <li>**...**: any additional properties from the options object when creating the
         *       cache.</li>
         *   </ul>
         */
        info: function() {
          return extend({}, stats, {size: size});
        }
      });


      /**
       * makes the `entry` the freshEnd of the LRU linked list
       */
      function refresh(entry) {
        if (entry !== freshEnd) {
          if (!staleEnd) {
            staleEnd = entry;
          } else if (staleEnd === entry) {
            staleEnd = entry.n;
          }

          link(entry.n, entry.p);
          link(entry, freshEnd);
          freshEnd = entry;
          freshEnd.n = null;
        }
      }


      /**
       * bidirectionally links two entries of the LRU linked list
       */
      function link(nextEntry, prevEntry) {
        if (nextEntry !== prevEntry) {
          if (nextEntry) nextEntry.p = prevEntry; //p stands for previous, 'prev' didn't minify
          if (prevEntry) prevEntry.n = nextEntry; //n stands for next, 'next' didn't minify
        }
      }
    }


  /**
   * @ngdoc method
   * @name $cacheFactory#info
   *
   * @description
   * Get information about all the caches that have been created
   *
   * @returns {Object} - key-value map of `cacheId` to the result of calling `cache#info`
   */
    cacheFactory.info = function() {
      var info = {};
      forEach(caches, function(cache, cacheId) {
        info[cacheId] = cache.info();
      });
      return info;
    };


  /**
   * @ngdoc method
   * @name $cacheFactory#get
   *
   * @description
   * Get access to a cache object by the `cacheId` used when it was created.
   *
   * @param {string} cacheId Name or id of a cache to access.
   * @returns {object} Cache object identified by the cacheId or undefined if no such cache.
   */
    cacheFactory.get = function(cacheId) {
      return caches[cacheId];
    };


    return cacheFactory;
  };
}

/**
 * @ngdoc service
 * @name $templateCache
 * @this
 *
 * @description
 * The first time a template is used, it is loaded in the template cache for quick retrieval. You
 * can load templates directly into the cache in a `script` tag, or by consuming the
 * `$templateCache` service directly.
 *
 * Adding via the `script` tag:
 *
 * ```html
 *   <script type="text/ng-template" id="templateId.html">
 *     <p>This is the content of the template</p>
 *   </script>
 * ```
 *
 * **Note:** the `script` tag containing the template does not need to be included in the `head` of
 * the document, but it must be a descendent of the {@link ng.$rootElement $rootElement} (IE,
 * element with ng-app attribute), otherwise the template will be ignored.
 *
 * Adding via the `$templateCache` service:
 *
 * ```js
 * var myApp = angular.module('myApp', []);
 * myApp.run(function($templateCache) {
 *   $templateCache.put('templateId.html', 'This is the content of the template');
 * });
 * ```
 *
 * To retrieve the template later, simply use it in your component:
 * ```js
 * myApp.component('myComponent', {
 *    templateUrl: 'templateId.html'
 * });
 * ```
 *
 * or get it via the `$templateCache` service:
 * ```js
 * $templateCache.get('templateId.html')
 * ```
 *
 * See {@link ng.$cacheFactory $cacheFactory}.
 *
 */
function $TemplateCacheProvider() {
  this.$get = ['$cacheFactory', function($cacheFactory) {
    return $cacheFactory('templates');
  }];
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables like document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/* ! VARIABLE/FUNCTION NAMING CONVENTIONS THAT APPLY TO THIS FILE!
 *
 * DOM-related variables:
 *
 * - "node" - DOM Node
 * - "element" - DOM Element or Node
 * - "$node" or "$element" - jqLite-wrapped node or element
 *
 *
 * Compiler related stuff:
 *
 * - "linkFn" - linking fn of a single directive
 * - "nodeLinkFn" - function that aggregates all linking fns for a particular node
 * - "childLinkFn" -  function that aggregates all linking fns for child nodes of a particular node
 * - "compositeLinkFn" - function that aggregates all linking fns for a compilation root (nodeList)
 */


/**
 * @ngdoc service
 * @name $compile
 * @kind function
 *
 * @description
 * Compiles an HTML string or DOM into a template and produces a template function, which
 * can then be used to link {@link ng.$rootScope.Scope `scope`} and the template together.
 *
 * The compilation is a process of walking the DOM tree and matching DOM elements to
 * {@link ng.$compileProvider#directive directives}.
 *
 * <div class="alert alert-warning">
 * **Note:** This document is an in-depth reference of all directive options.
 * For a gentle introduction to directives with examples of common use cases,
 * see the {@link guide/directive directive guide}.
 * </div>
 *
 * ## Comprehensive Directive API
 *
 * There are many different options for a directive.
 *
 * The difference resides in the return value of the factory function.
 * You can either return a {@link $compile#directive-definition-object Directive Definition Object (see below)}
 * that defines the directive properties, or just the `postLink` function (all other properties will have
 * the default values).
 *
 * <div class="alert alert-success">
 * **Best Practice:** It's recommended to use the "directive definition object" form.
 * </div>
 *
 * Here's an example directive declared with a Directive Definition Object:
 *
 * ```js
 *   var myModule = angular.module(...);
 *
 *   myModule.directive('directiveName', function factory(injectables) {
 *     var directiveDefinitionObject = {
 *       {@link $compile#-priority- priority}: 0,
 *       {@link $compile#-template- template}: '<div></div>', // or // function(tElement, tAttrs) { ... },
 *       // or
 *       // {@link $compile#-templateurl- templateUrl}: 'directive.html', // or // function(tElement, tAttrs) { ... },
 *       {@link $compile#-transclude- transclude}: false,
 *       {@link $compile#-restrict- restrict}: 'A',
 *       {@link $compile#-templatenamespace- templateNamespace}: 'html',
 *       {@link $compile#-scope- scope}: false,
 *       {@link $compile#-controller- controller}: function($scope, $element, $attrs, $transclude, otherInjectables) { ... },
 *       {@link $compile#-controlleras- controllerAs}: 'stringIdentifier',
 *       {@link $compile#-bindtocontroller- bindToController}: false,
 *       {@link $compile#-require- require}: 'siblingDirectiveName', // or // ['^parentDirectiveName', '?optionalDirectiveName', '?^optionalParent'],
 *       {@link $compile#-multielement- multiElement}: false,
 *       {@link $compile#-compile- compile}: function compile(tElement, tAttrs, transclude) {
 *         return {
 *            {@link $compile#pre-linking-function pre}: function preLink(scope, iElement, iAttrs, controller) { ... },
 *            {@link $compile#post-linking-function post}: function postLink(scope, iElement, iAttrs, controller) { ... }
 *         }
 *         // or
 *         // return function postLink( ... ) { ... }
 *       },
 *       // or
 *       // {@link $compile#-link- link}: {
 *       //  {@link $compile#pre-linking-function pre}: function preLink(scope, iElement, iAttrs, controller) { ... },
 *       //  {@link $compile#post-linking-function post}: function postLink(scope, iElement, iAttrs, controller) { ... }
 *       // }
 *       // or
 *       // {@link $compile#-link- link}: function postLink( ... ) { ... }
 *     };
 *     return directiveDefinitionObject;
 *   });
 * ```
 *
 * <div class="alert alert-warning">
 * **Note:** Any unspecified options will use the default value. You can see the default values below.
 * </div>
 *
 * Therefore the above can be simplified as:
 *
 * ```js
 *   var myModule = angular.module(...);
 *
 *   myModule.directive('directiveName', function factory(injectables) {
 *     var directiveDefinitionObject = {
 *       link: function postLink(scope, iElement, iAttrs) { ... }
 *     };
 *     return directiveDefinitionObject;
 *     // or
 *     // return function postLink(scope, iElement, iAttrs) { ... }
 *   });
 * ```
 *
 * ### Life-cycle hooks
 * Directive controllers can provide the following methods that are called by Angular at points in the life-cycle of the
 * directive:
 * * `$onInit()` - Called on each controller after all the controllers on an element have been constructed and
 *   had their bindings initialized (and before the pre &amp; post linking functions for the directives on
 *   this element). This is a good place to put initialization code for your controller.
 * * `$onChanges(changesObj)` - Called whenever one-way (`<`) or interpolation (`@`) bindings are updated. The
 *   `changesObj` is a hash whose keys are the names of the bound properties that have changed, and the values are an
 *   object of the form `{ currentValue, previousValue, isFirstChange() }`. Use this hook to trigger updates within a
 *   component such as cloning the bound value to prevent accidental mutation of the outer value. Note that this will
 *   also be called when your bindings are initialized.
 * * `$doCheck()` - Called on each turn of the digest cycle. Provides an opportunity to detect and act on
 *   changes. Any actions that you wish to take in response to the changes that you detect must be
 *   invoked from this hook; implementing this has no effect on when `$onChanges` is called. For example, this hook
 *   could be useful if you wish to perform a deep equality check, or to check a Date object, changes to which would not
 *   be detected by Angular's change detector and thus not trigger `$onChanges`. This hook is invoked with no arguments;
 *   if detecting changes, you must store the previous value(s) for comparison to the current values.
 * * `$onDestroy()` - Called on a controller when its containing scope is destroyed. Use this hook for releasing
 *   external resources, watches and event handlers. Note that components have their `$onDestroy()` hooks called in
 *   the same order as the `$scope.$broadcast` events are triggered, which is top down. This means that parent
 *   components will have their `$onDestroy()` hook called before child components.
 * * `$postLink()` - Called after this controller's element and its children have been linked. Similar to the post-link
 *   function this hook can be used to set up DOM event handlers and do direct DOM manipulation.
 *   Note that child elements that contain `templateUrl` directives will not have been compiled and linked since
 *   they are waiting for their template to load asynchronously and their own compilation and linking has been
 *   suspended until that occurs.
 *
 * #### Comparison with Angular 2 life-cycle hooks
 * Angular 2 also uses life-cycle hooks for its components. While the Angular 1 life-cycle hooks are similar there are
 * some differences that you should be aware of, especially when it comes to moving your code from Angular 1 to Angular 2:
 *
 * * Angular 1 hooks are prefixed with `$`, such as `$onInit`. Angular 2 hooks are prefixed with `ng`, such as `ngOnInit`.
 * * Angular 1 hooks can be defined on the controller prototype or added to the controller inside its constructor.
 *   In Angular 2 you can only define hooks on the prototype of the Component class.
 * * Due to the differences in change-detection, you may get many more calls to `$doCheck` in Angular 1 than you would to
 *   `ngDoCheck` in Angular 2
 * * Changes to the model inside `$doCheck` will trigger new turns of the digest loop, which will cause the changes to be
 *   propagated throughout the application.
 *   Angular 2 does not allow the `ngDoCheck` hook to trigger a change outside of the component. It will either throw an
 *   error or do nothing depending upon the state of `enableProdMode()`.
 *
 * #### Life-cycle hook examples
 *
 * This example shows how you can check for mutations to a Date object even though the identity of the object
 * has not changed.
 *
 * <example name="doCheckDateExample" module="do-check-module">
 *   <file name="app.js">
 *     angular.module('do-check-module', [])
 *       .component('app', {
 *         template:
 *           'Month: <input ng-model="$ctrl.month" ng-change="$ctrl.updateDate()">' +
 *           'Date: {{ $ctrl.date }}' +
 *           '<test date="$ctrl.date"></test>',
 *         controller: function() {
 *           this.date = new Date();
 *           this.month = this.date.getMonth();
 *           this.updateDate = function() {
 *             this.date.setMonth(this.month);
 *           };
 *         }
 *       })
 *       .component('test', {
 *         bindings: { date: '<' },
 *         template:
 *           '<pre>{{ $ctrl.log | json }}</pre>',
 *         controller: function() {
 *           var previousValue;
 *           this.log = [];
 *           this.$doCheck = function() {
 *             var currentValue = this.date && this.date.valueOf();
 *             if (previousValue !== currentValue) {
 *               this.log.push('doCheck: date mutated: ' + this.date);
 *               previousValue = currentValue;
 *             }
 *           };
 *         }
 *       });
 *   </file>
 *   <file name="index.html">
 *     <app></app>
 *   </file>
 * </example>
 *
 * This example show how you might use `$doCheck` to trigger changes in your component's inputs even if the
 * actual identity of the component doesn't change. (Be aware that cloning and deep equality checks on large
 * arrays or objects can have a negative impact on your application performance)
 *
 * <example name="doCheckArrayExample" module="do-check-module">
 *   <file name="index.html">
 *     <div ng-init="items = []">
 *       <button ng-click="items.push(items.length)">Add Item</button>
 *       <button ng-click="items = []">Reset Items</button>
 *       <pre>{{ items }}</pre>
 *       <test items="items"></test>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *      angular.module('do-check-module', [])
 *        .component('test', {
 *          bindings: { items: '<' },
 *          template:
 *            '<pre>{{ $ctrl.log | json }}</pre>',
 *          controller: function() {
 *            this.log = [];
 *
 *            this.$doCheck = function() {
 *              if (this.items_ref !== this.items) {
 *                this.log.push('doCheck: items changed');
 *                this.items_ref = this.items;
 *              }
 *              if (!angular.equals(this.items_clone, this.items)) {
 *                this.log.push('doCheck: items mutated');
 *                this.items_clone = angular.copy(this.items);
 *              }
 *            };
 *          }
 *        });
 *   </file>
 * </example>
 *
 *
 * ### Directive Definition Object
 *
 * The directive definition object provides instructions to the {@link ng.$compile
 * compiler}. The attributes are:
 *
 * #### `multiElement`
 * When this property is set to true (default is `false`), the HTML compiler will collect DOM nodes between
 * nodes with the attributes `directive-name-start` and `directive-name-end`, and group them
 * together as the directive elements. It is recommended that this feature be used on directives
 * which are not strictly behavioral (such as {@link ngClick}), and which
 * do not manipulate or replace child nodes (such as {@link ngInclude}).
 *
 * #### `priority`
 * When there are multiple directives defined on a single DOM element, sometimes it
 * is necessary to specify the order in which the directives are applied. The `priority` is used
 * to sort the directives before their `compile` functions get called. Priority is defined as a
 * number. Directives with greater numerical `priority` are compiled first. Pre-link functions
 * are also run in priority order, but post-link functions are run in reverse order. The order
 * of directives with the same priority is undefined. The default priority is `0`.
 *
 * #### `terminal`
 * If set to true then the current `priority` will be the last set of directives
 * which will execute (any directives at the current priority will still execute
 * as the order of execution on same `priority` is undefined). Note that expressions
 * and other directives used in the directive's template will also be excluded from execution.
 *
 * #### `scope`
 * The scope property can be `false`, `true`, or an object:
 *
 * * **`false` (default):** No scope will be created for the directive. The directive will use its
 * parent's scope.
 *
 * * **`true`:** A new child scope that prototypically inherits from its parent will be created for
 * the directive's element. If multiple directives on the same element request a new scope,
 * only one new scope is created.
 *
 * * **`{...}` (an object hash):** A new "isolate" scope is created for the directive's template.
 * The 'isolate' scope differs from normal scope in that it does not prototypically
 * inherit from its parent scope. This is useful when creating reusable components, which should not
 * accidentally read or modify data in the parent scope. Note that an isolate scope
 * directive without a `template` or `templateUrl` will not apply the isolate scope
 * to its children elements.
 *
 * The 'isolate' scope object hash defines a set of local scope properties derived from attributes on the
 * directive's element. These local properties are useful for aliasing values for templates. The keys in
 * the object hash map to the name of the property on the isolate scope; the values define how the property
 * is bound to the parent scope, via matching attributes on the directive's element:
 *
 * * `@` or `@attr` - bind a local scope property to the value of DOM attribute. The result is
 *   always a string since DOM attributes are strings. If no `attr` name is specified then the
 *   attribute name is assumed to be the same as the local name. Given `<my-component
 *   my-attr="hello {{name}}">` and the isolate scope definition `scope: { localName:'@myAttr' }`,
 *   the directive's scope property `localName` will reflect the interpolated value of `hello
 *   {{name}}`. As the `name` attribute changes so will the `localName` property on the directive's
 *   scope. The `name` is read from the parent scope (not the directive's scope).
 *
 * * `=` or `=attr` - set up a bidirectional binding between a local scope property and an expression
 *   passed via the attribute `attr`. The expression is evaluated in the context of the parent scope.
 *   If no `attr` name is specified then the attribute name is assumed to be the same as the local
 *   name. Given `<my-component my-attr="parentModel">` and the isolate scope definition `scope: {
 *   localModel: '=myAttr' }`, the property `localModel` on the directive's scope will reflect the
 *   value of `parentModel` on the parent scope. Changes to `parentModel` will be reflected in
 *   `localModel` and vice versa. Optional attributes should be marked as such with a question mark:
 *   `=?` or `=?attr`. If the binding expression is non-assignable, or if the attribute isn't
 *   optional and doesn't exist, an exception ({@link error/$compile/nonassign `$compile:nonassign`})
 *   will be thrown upon discovering changes to the local value, since it will be impossible to sync
 *   them back to the parent scope. By default, the {@link ng.$rootScope.Scope#$watch `$watch`}
 *   method is used for tracking changes, and the equality check is based on object identity.
 *   However, if an object literal or an array literal is passed as the binding expression, the
 *   equality check is done by value (using the {@link angular.equals} function). It's also possible
 *   to watch the evaluated value shallowly with {@link ng.$rootScope.Scope#$watchCollection
 *   `$watchCollection`}: use `=*` or `=*attr` (`=*?` or `=*?attr` if the attribute is optional).
 *
  * * `<` or `<attr` - set up a one-way (one-directional) binding between a local scope property and an
 *   expression passed via the attribute `attr`. The expression is evaluated in the context of the
 *   parent scope. If no `attr` name is specified then the attribute name is assumed to be the same as the
 *   local name. You can also make the binding optional by adding `?`: `<?` or `<?attr`.
 *
 *   For example, given `<my-component my-attr="parentModel">` and directive definition of
 *   `scope: { localModel:'<myAttr' }`, then the isolated scope property `localModel` will reflect the
 *   value of `parentModel` on the parent scope. Any changes to `parentModel` will be reflected
 *   in `localModel`, but changes in `localModel` will not reflect in `parentModel`. There are however
 *   two caveats:
 *     1. one-way binding does not copy the value from the parent to the isolate scope, it simply
 *     sets the same value. That means if your bound value is an object, changes to its properties
 *     in the isolated scope will be reflected in the parent scope (because both reference the same object).
 *     2. one-way binding watches changes to the **identity** of the parent value. That means the
 *     {@link ng.$rootScope.Scope#$watch `$watch`} on the parent value only fires if the reference
 *     to the value has changed. In most cases, this should not be of concern, but can be important
 *     to know if you one-way bind to an object, and then replace that object in the isolated scope.
 *     If you now change a property of the object in your parent scope, the change will not be
 *     propagated to the isolated scope, because the identity of the object on the parent scope
 *     has not changed. Instead you must assign a new object.
 *
 *   One-way binding is useful if you do not plan to propagate changes to your isolated scope bindings
 *   back to the parent. However, it does not make this completely impossible.
 *
 * * `&` or `&attr` - provides a way to execute an expression in the context of the parent scope. If
 *   no `attr` name is specified then the attribute name is assumed to be the same as the local name.
 *   Given `<my-component my-attr="count = count + value">` and the isolate scope definition `scope: {
 *   localFn:'&myAttr' }`, the isolate scope property `localFn` will point to a function wrapper for
 *   the `count = count + value` expression. Often it's desirable to pass data from the isolated scope
 *   via an expression to the parent scope. This can be done by passing a map of local variable names
 *   and values into the expression wrapper fn. For example, if the expression is `increment(amount)`
 *   then we can specify the amount value by calling the `localFn` as `localFn({amount: 22})`.
 *
 * In general it's possible to apply more than one directive to one element, but there might be limitations
 * depending on the type of scope required by the directives. The following points will help explain these limitations.
 * For simplicity only two directives are taken into account, but it is also applicable for several directives:
 *
 * * **no scope** + **no scope** => Two directives which don't require their own scope will use their parent's scope
 * * **child scope** + **no scope** =>  Both directives will share one single child scope
 * * **child scope** + **child scope** =>  Both directives will share one single child scope
 * * **isolated scope** + **no scope** =>  The isolated directive will use it's own created isolated scope. The other directive will use
 * its parent's scope
 * * **isolated scope** + **child scope** =>  **Won't work!** Only one scope can be related to one element. Therefore these directives cannot
 * be applied to the same element.
 * * **isolated scope** + **isolated scope**  =>  **Won't work!** Only one scope can be related to one element. Therefore these directives
 * cannot be applied to the same element.
 *
 *
 * #### `bindToController`
 * This property is used to bind scope properties directly to the controller. It can be either
 * `true` or an object hash with the same format as the `scope` property.
 *
 * When an isolate scope is used for a directive (see above), `bindToController: true` will
 * allow a component to have its properties bound to the controller, rather than to scope.
 *
 * After the controller is instantiated, the initial values of the isolate scope bindings will be bound to the controller
 * properties. You can access these bindings once they have been initialized by providing a controller method called
 * `$onInit`, which is called after all the controllers on an element have been constructed and had their bindings
 * initialized.
 *
 * <div class="alert alert-warning">
 * **Deprecation warning:** although bindings for non-ES6 class controllers are currently
 * bound to `this` before the controller constructor is called, this use is now deprecated. Please place initialization
 * code that relies upon bindings inside a `$onInit` method on the controller, instead.
 * </div>
 *
 * It is also possible to set `bindToController` to an object hash with the same format as the `scope` property.
 * This will set up the scope bindings to the controller directly. Note that `scope` can still be used
 * to define which kind of scope is created. By default, no scope is created. Use `scope: {}` to create an isolate
 * scope (useful for component directives).
 *
 * If both `bindToController` and `scope` are defined and have object hashes, `bindToController` overrides `scope`.
 *
 *
 * #### `controller`
 * Controller constructor function. The controller is instantiated before the
 * pre-linking phase and can be accessed by other directives (see
 * `require` attribute). This allows the directives to communicate with each other and augment
 * each other's behavior. The controller is injectable (and supports bracket notation) with the following locals:
 *
 * * `$scope` - Current scope associated with the element
 * * `$element` - Current element
 * * `$attrs` - Current attributes object for the element
 * * `$transclude` - A transclude linking function pre-bound to the correct transclusion scope:
 *   `function([scope], cloneLinkingFn, futureParentElement, slotName)`:
 *    * `scope`: (optional) override the scope.
 *    * `cloneLinkingFn`: (optional) argument to create clones of the original transcluded content.
 *    * `futureParentElement` (optional):
 *        * defines the parent to which the `cloneLinkingFn` will add the cloned elements.
 *        * default: `$element.parent()` resp. `$element` for `transclude:'element'` resp. `transclude:true`.
 *        * only needed for transcludes that are allowed to contain non html elements (e.g. SVG elements)
 *          and when the `cloneLinkingFn` is passed,
 *          as those elements need to created and cloned in a special way when they are defined outside their
 *          usual containers (e.g. like `<svg>`).
 *        * See also the `directive.templateNamespace` property.
 *    * `slotName`: (optional) the name of the slot to transclude. If falsy (e.g. `null`, `undefined` or `''`)
 *      then the default transclusion is provided.
 *    The `$transclude` function also has a method on it, `$transclude.isSlotFilled(slotName)`, which returns
 *    `true` if the specified slot contains content (i.e. one or more DOM nodes).
 *
 * #### `require`
 * Require another directive and inject its controller as the fourth argument to the linking function. The
 * `require` property can be a string, an array or an object:
 * * a **string** containing the name of the directive to pass to the linking function
 * * an **array** containing the names of directives to pass to the linking function. The argument passed to the
 * linking function will be an array of controllers in the same order as the names in the `require` property
 * * an **object** whose property values are the names of the directives to pass to the linking function. The argument
 * passed to the linking function will also be an object with matching keys, whose values will hold the corresponding
 * controllers.
 *
 * If the `require` property is an object and `bindToController` is truthy, then the required controllers are
 * bound to the controller using the keys of the `require` property. This binding occurs after all the controllers
 * have been constructed but before `$onInit` is called.
 * If the name of the required controller is the same as the local name (the key), the name can be
 * omitted. For example, `{parentDir: '^^'}` is equivalent to `{parentDir: '^^parentDir'}`.
 * See the {@link $compileProvider#component} helper for an example of how this can be used.
 * If no such required directive(s) can be found, or if the directive does not have a controller, then an error is
 * raised (unless no link function is specified and the required controllers are not being bound to the directive
 * controller, in which case error checking is skipped). The name can be prefixed with:
 *
 * * (no prefix) - Locate the required controller on the current element. Throw an error if not found.
 * * `?` - Attempt to locate the required controller or pass `null` to the `link` fn if not found.
 * * `^` - Locate the required controller by searching the element and its parents. Throw an error if not found.
 * * `^^` - Locate the required controller by searching the element's parents. Throw an error if not found.
 * * `?^` - Attempt to locate the required controller by searching the element and its parents or pass
 *   `null` to the `link` fn if not found.
 * * `?^^` - Attempt to locate the required controller by searching the element's parents, or pass
 *   `null` to the `link` fn if not found.
 *
 *
 * #### `controllerAs`
 * Identifier name for a reference to the controller in the directive's scope.
 * This allows the controller to be referenced from the directive template. This is especially
 * useful when a directive is used as component, i.e. with an `isolate` scope. It's also possible
 * to use it in a directive without an `isolate` / `new` scope, but you need to be aware that the
 * `controllerAs` reference might overwrite a property that already exists on the parent scope.
 *
 *
 * #### `restrict`
 * String of subset of `EACM` which restricts the directive to a specific directive
 * declaration style. If omitted, the defaults (elements and attributes) are used.
 *
 * * `E` - Element name (default): `<my-directive></my-directive>`
 * * `A` - Attribute (default): `<div my-directive="exp"></div>`
 * * `C` - Class: `<div class="my-directive: exp;"></div>`
 * * `M` - Comment: `<!-- directive: my-directive exp -->`
 *
 *
 * #### `templateNamespace`
 * String representing the document type used by the markup in the template.
 * AngularJS needs this information as those elements need to be created and cloned
 * in a special way when they are defined outside their usual containers like `<svg>` and `<math>`.
 *
 * * `html` - All root nodes in the template are HTML. Root nodes may also be
 *   top-level elements such as `<svg>` or `<math>`.
 * * `svg` - The root nodes in the template are SVG elements (excluding `<math>`).
 * * `math` - The root nodes in the template are MathML elements (excluding `<svg>`).
 *
 * If no `templateNamespace` is specified, then the namespace is considered to be `html`.
 *
 * #### `template`
 * HTML markup that may:
 * * Replace the contents of the directive's element (default).
 * * Replace the directive's element itself (if `replace` is true - DEPRECATED).
 * * Wrap the contents of the directive's element (if `transclude` is true).
 *
 * Value may be:
 *
 * * A string. For example `<div red-on-hover>{{delete_str}}</div>`.
 * * A function which takes two arguments `tElement` and `tAttrs` (described in the `compile`
 *   function api below) and returns a string value.
 *
 *
 * #### `templateUrl`
 * This is similar to `template` but the template is loaded from the specified URL, asynchronously.
 *
 * Because template loading is asynchronous the compiler will suspend compilation of directives on that element
 * for later when the template has been resolved.  In the meantime it will continue to compile and link
 * sibling and parent elements as though this element had not contained any directives.
 *
 * The compiler does not suspend the entire compilation to wait for templates to be loaded because this
 * would result in the whole app "stalling" until all templates are loaded asynchronously - even in the
 * case when only one deeply nested directive has `templateUrl`.
 *
 * Template loading is asynchronous even if the template has been preloaded into the {@link $templateCache}
 *
 * You can specify `templateUrl` as a string representing the URL or as a function which takes two
 * arguments `tElement` and `tAttrs` (described in the `compile` function api below) and returns
 * a string value representing the url.  In either case, the template URL is passed through {@link
 * $sce#getTrustedResourceUrl $sce.getTrustedResourceUrl}.
 *
 *
 * #### `replace` ([*DEPRECATED*!], will be removed in next major release - i.e. v2.0)
 * specify what the template should replace. Defaults to `false`.
 *
 * * `true` - the template will replace the directive's element.
 * * `false` - the template will replace the contents of the directive's element.
 *
 * The replacement process migrates all of the attributes / classes from the old element to the new
 * one. See the {@link guide/directive#template-expanding-directive
 * Directives Guide} for an example.
 *
 * There are very few scenarios where element replacement is required for the application function,
 * the main one being reusable custom components that are used within SVG contexts
 * (because SVG doesn't work with custom elements in the DOM tree).
 *
 * #### `transclude`
 * Extract the contents of the element where the directive appears and make it available to the directive.
 * The contents are compiled and provided to the directive as a **transclusion function**. See the
 * {@link $compile#transclusion Transclusion} section below.
 *
 *
 * #### `compile`
 *
 * ```js
 *   function compile(tElement, tAttrs, transclude) { ... }
 * ```
 *
 * The compile function deals with transforming the template DOM. Since most directives do not do
 * template transformation, it is not used often. The compile function takes the following arguments:
 *
 *   * `tElement` - template element - The element where the directive has been declared. It is
 *     safe to do template transformation on the element and child elements only.
 *
 *   * `tAttrs` - template attributes - Normalized list of attributes declared on this element shared
 *     between all directive compile functions.
 *
 *   * `transclude` -  [*DEPRECATED*!] A transclude linking function: `function(scope, cloneLinkingFn)`
 *
 * <div class="alert alert-warning">
 * **Note:** The template instance and the link instance may be different objects if the template has
 * been cloned. For this reason it is **not** safe to do anything other than DOM transformations that
 * apply to all cloned DOM nodes within the compile function. Specifically, DOM listener registration
 * should be done in a linking function rather than in a compile function.
 * </div>

 * <div class="alert alert-warning">
 * **Note:** The compile function cannot handle directives that recursively use themselves in their
 * own templates or compile functions. Compiling these directives results in an infinite loop and
 * stack overflow errors.
 *
 * This can be avoided by manually using $compile in the postLink function to imperatively compile
 * a directive's template instead of relying on automatic template compilation via `template` or
 * `templateUrl` declaration or manual compilation inside the compile function.
 * </div>
 *
 * <div class="alert alert-danger">
 * **Note:** The `transclude` function that is passed to the compile function is deprecated, as it
 *   e.g. does not know about the right outer scope. Please use the transclude function that is passed
 *   to the link function instead.
 * </div>

 * A compile function can have a return value which can be either a function or an object.
 *
 * * returning a (post-link) function - is equivalent to registering the linking function via the
 *   `link` property of the config object when the compile function is empty.
 *
 * * returning an object with function(s) registered via `pre` and `post` properties - allows you to
 *   control when a linking function should be called during the linking phase. See info about
 *   pre-linking and post-linking functions below.
 *
 *
 * #### `link`
 * This property is used only if the `compile` property is not defined.
 *
 * ```js
 *   function link(scope, iElement, iAttrs, controller, transcludeFn) { ... }
 * ```
 *
 * The link function is responsible for registering DOM listeners as well as updating the DOM. It is
 * executed after the template has been cloned. This is where most of the directive logic will be
 * put.
 *
 *   * `scope` - {@link ng.$rootScope.Scope Scope} - The scope to be used by the
 *     directive for registering {@link ng.$rootScope.Scope#$watch watches}.
 *
 *   * `iElement` - instance element - The element where the directive is to be used. It is safe to
 *     manipulate the children of the element only in `postLink` function since the children have
 *     already been linked.
 *
 *   * `iAttrs` - instance attributes - Normalized list of attributes declared on this element shared
 *     between all directive linking functions.
 *
 *   * `controller` - the directive's required controller instance(s) - Instances are shared
 *     among all directives, which allows the directives to use the controllers as a communication
 *     channel. The exact value depends on the directive's `require` property:
 *       * no controller(s) required: the directive's own controller, or `undefined` if it doesn't have one
 *       * `string`: the controller instance
 *       * `array`: array of controller instances
 *
 *     If a required controller cannot be found, and it is optional, the instance is `null`,
 *     otherwise the {@link error:$compile:ctreq Missing Required Controller} error is thrown.
 *
 *     Note that you can also require the directive's own controller - it will be made available like
 *     any other controller.
 *
 *   * `transcludeFn` - A transclude linking function pre-bound to the correct transclusion scope.
 *     This is the same as the `$transclude` parameter of directive controllers,
 *     see {@link ng.$compile#-controller- the controller section for details}.
 *     `function([scope], cloneLinkingFn, futureParentElement)`.
 *
 * #### Pre-linking function
 *
 * Executed before the child elements are linked. Not safe to do DOM transformation since the
 * compiler linking function will fail to locate the correct elements for linking.
 *
 * #### Post-linking function
 *
 * Executed after the child elements are linked.
 *
 * Note that child elements that contain `templateUrl` directives will not have been compiled
 * and linked since they are waiting for their template to load asynchronously and their own
 * compilation and linking has been suspended until that occurs.
 *
 * It is safe to do DOM transformation in the post-linking function on elements that are not waiting
 * for their async templates to be resolved.
 *
 *
 * ### Transclusion
 *
 * Transclusion is the process of extracting a collection of DOM elements from one part of the DOM and
 * copying them to another part of the DOM, while maintaining their connection to the original AngularJS
 * scope from where they were taken.
 *
 * Transclusion is used (often with {@link ngTransclude}) to insert the
 * original contents of a directive's element into a specified place in the template of the directive.
 * The benefit of transclusion, over simply moving the DOM elements manually, is that the transcluded
 * content has access to the properties on the scope from which it was taken, even if the directive
 * has isolated scope.
 * See the {@link guide/directive#creating-a-directive-that-wraps-other-elements Directives Guide}.
 *
 * This makes it possible for the widget to have private state for its template, while the transcluded
 * content has access to its originating scope.
 *
 * <div class="alert alert-warning">
 * **Note:** When testing an element transclude directive you must not place the directive at the root of the
 * DOM fragment that is being compiled. See {@link guide/unit-testing#testing-transclusion-directives
 * Testing Transclusion Directives}.
 * </div>
 *
 * There are three kinds of transclusion depending upon whether you want to transclude just the contents of the
 * directive's element, the entire element or multiple parts of the element contents:
 *
 * * `true` - transclude the content (i.e. the child nodes) of the directive's element.
 * * `'element'` - transclude the whole of the directive's element including any directives on this
 *   element that defined at a lower priority than this directive. When used, the `template`
 *   property is ignored.
 * * **`{...}` (an object hash):** - map elements of the content onto transclusion "slots" in the template.
 *
 * **Mult-slot transclusion** is declared by providing an object for the `transclude` property.
 *
 * This object is a map where the keys are the name of the slot to fill and the value is an element selector
 * used to match the HTML to the slot. The element selector should be in normalized form (e.g. `myElement`)
 * and will match the standard element variants (e.g. `my-element`, `my:element`, `data-my-element`, etc).
 *
 * For further information check out the guide on {@link guide/directive#matching-directives Matching Directives}
 *
 * If the element selector is prefixed with a `?` then that slot is optional.
 *
 * For example, the transclude object `{ slotA: '?myCustomElement' }` maps `<my-custom-element>` elements to
 * the `slotA` slot, which can be accessed via the `$transclude` function or via the {@link ngTransclude} directive.
 *
 * Slots that are not marked as optional (`?`) will trigger a compile time error if there are no matching elements
 * in the transclude content. If you wish to know if an optional slot was filled with content, then you can call
 * `$transclude.isSlotFilled(slotName)` on the transclude function passed to the directive's link function and
 * injectable into the directive's controller.
 *
 *
 * #### Transclusion Functions
 *
 * When a directive requests transclusion, the compiler extracts its contents and provides a **transclusion
 * function** to the directive's `link` function and `controller`. This transclusion function is a special
 * **linking function** that will return the compiled contents linked to a new transclusion scope.
 *
 * <div class="alert alert-info">
 * If you are just using {@link ngTransclude} then you don't need to worry about this function, since
 * ngTransclude will deal with it for us.
 * </div>
 *
 * If you want to manually control the insertion and removal of the transcluded content in your directive
 * then you must use this transclude function. When you call a transclude function it returns a a jqLite/JQuery
 * object that contains the compiled DOM, which is linked to the correct transclusion scope.
 *
 * When you call a transclusion function you can pass in a **clone attach function**. This function accepts
 * two parameters, `function(clone, scope) { ... }`, where the `clone` is a fresh compiled copy of your transcluded
 * content and the `scope` is the newly created transclusion scope, which the clone will be linked to.
 *
 * <div class="alert alert-info">
 * **Best Practice**: Always provide a `cloneFn` (clone attach function) when you call a transclude function
 * since you then get a fresh clone of the original DOM and also have access to the new transclusion scope.
 * </div>
 *
 * It is normal practice to attach your transcluded content (`clone`) to the DOM inside your **clone
 * attach function**:
 *
 * ```js
 * var transcludedContent, transclusionScope;
 *
 * $transclude(function(clone, scope) {
 *   element.append(clone);
 *   transcludedContent = clone;
 *   transclusionScope = scope;
 * });
 * ```
 *
 * Later, if you want to remove the transcluded content from your DOM then you should also destroy the
 * associated transclusion scope:
 *
 * ```js
 * transcludedContent.remove();
 * transclusionScope.$destroy();
 * ```
 *
 * <div class="alert alert-info">
 * **Best Practice**: if you intend to add and remove transcluded content manually in your directive
 * (by calling the transclude function to get the DOM and calling `element.remove()` to remove it),
 * then you are also responsible for calling `$destroy` on the transclusion scope.
 * </div>
 *
 * The built-in DOM manipulation directives, such as {@link ngIf}, {@link ngSwitch} and {@link ngRepeat}
 * automatically destroy their transcluded clones as necessary so you do not need to worry about this if
 * you are simply using {@link ngTransclude} to inject the transclusion into your directive.
 *
 *
 * #### Transclusion Scopes
 *
 * When you call a transclude function it returns a DOM fragment that is pre-bound to a **transclusion
 * scope**. This scope is special, in that it is a child of the directive's scope (and so gets destroyed
 * when the directive's scope gets destroyed) but it inherits the properties of the scope from which it
 * was taken.
 *
 * For example consider a directive that uses transclusion and isolated scope. The DOM hierarchy might look
 * like this:
 *
 * ```html
 * <div ng-app>
 *   <div isolate>
 *     <div transclusion>
 *     </div>
 *   </div>
 * </div>
 * ```
 *
 * The `$parent` scope hierarchy will look like this:
 *
   ```
   - $rootScope
     - isolate
       - transclusion
   ```
 *
 * but the scopes will inherit prototypically from different scopes to their `$parent`.
 *
   ```
   - $rootScope
     - transclusion
   - isolate
   ```
 *
 *
 * ### Attributes
 *
 * The {@link ng.$compile.directive.Attributes Attributes} object - passed as a parameter in the
 * `link()` or `compile()` functions. It has a variety of uses.
 *
 * * *Accessing normalized attribute names:* Directives like 'ngBind' can be expressed in many ways:
 *   'ng:bind', `data-ng-bind`, or 'x-ng-bind'. The attributes object allows for normalized access
 *   to the attributes.
 *
 * * *Directive inter-communication:* All directives share the same instance of the attributes
 *   object which allows the directives to use the attributes object as inter directive
 *   communication.
 *
 * * *Supports interpolation:* Interpolation attributes are assigned to the attribute object
 *   allowing other directives to read the interpolated value.
 *
 * * *Observing interpolated attributes:* Use `$observe` to observe the value changes of attributes
 *   that contain interpolation (e.g. `src="{{bar}}"`). Not only is this very efficient but it's also
 *   the only way to easily get the actual value because during the linking phase the interpolation
 *   hasn't been evaluated yet and so the value is at this time set to `undefined`.
 *
 * ```js
 * function linkingFn(scope, elm, attrs, ctrl) {
 *   // get the attribute value
 *   console.log(attrs.ngModel);
 *
 *   // change the attribute
 *   attrs.$set('ngModel', 'new value');
 *
 *   // observe changes to interpolated attribute
 *   attrs.$observe('ngModel', function(value) {
 *     console.log('ngModel has changed value to ' + value);
 *   });
 * }
 * ```
 *
 * ## Example
 *
 * <div class="alert alert-warning">
 * **Note**: Typically directives are registered with `module.directive`. The example below is
 * to illustrate how `$compile` works.
 * </div>
 *
 <example module="compileExample" name="compile">
   <file name="index.html">
    <script>
      angular.module('compileExample', [], function($compileProvider) {
        // configure new 'compile' directive by passing a directive
        // factory function. The factory function injects the '$compile'
        $compileProvider.directive('compile', function($compile) {
          // directive factory creates a link function
          return function(scope, element, attrs) {
            scope.$watch(
              function(scope) {
                 // watch the 'compile' expression for changes
                return scope.$eval(attrs.compile);
              },
              function(value) {
                // when the 'compile' expression changes
                // assign it into the current DOM
                element.html(value);

                // compile the new DOM and link it to the current
                // scope.
                // NOTE: we only compile .childNodes so that
                // we don't get into infinite loop compiling ourselves
                $compile(element.contents())(scope);
              }
            );
          };
        });
      })
      .controller('GreeterController', ['$scope', function($scope) {
        $scope.name = 'Angular';
        $scope.html = 'Hello {{name}}';
      }]);
    </script>
    <div ng-controller="GreeterController">
      <input ng-model="name"> <br/>
      <textarea ng-model="html"></textarea> <br/>
      <div compile="html"></div>
    </div>
   </file>
   <file name="protractor.js" type="protractor">
     it('should auto compile', function() {
       var textarea = $('textarea');
       var output = $('div[compile]');
       // The initial state reads 'Hello Angular'.
       expect(output.getText()).toBe('Hello Angular');
       textarea.clear();
       textarea.sendKeys('{{name}}!');
       expect(output.getText()).toBe('Angular!');
     });
   </file>
 </example>

 *
 *
 * @param {string|DOMElement} element Element or HTML string to compile into a template function.
 * @param {function(angular.Scope, cloneAttachFn=)} transclude function available to directives - DEPRECATED.
 *
 * <div class="alert alert-danger">
 * **Note:** Passing a `transclude` function to the $compile function is deprecated, as it
 *   e.g. will not use the right outer scope. Please pass the transclude function as a
 *   `parentBoundTranscludeFn` to the link function instead.
 * </div>
 *
 * @param {number} maxPriority only apply directives lower than given priority (Only effects the
 *                 root element(s), not their children)
 * @returns {function(scope, cloneAttachFn=, options=)} a link function which is used to bind template
 * (a DOM element/tree) to a scope. Where:
 *
 *  * `scope` - A {@link ng.$rootScope.Scope Scope} to bind to.
 *  * `cloneAttachFn` - If `cloneAttachFn` is provided, then the link function will clone the
 *  `template` and call the `cloneAttachFn` function allowing the caller to attach the
 *  cloned elements to the DOM document at the appropriate place. The `cloneAttachFn` is
 *  called as: <br/> `cloneAttachFn(clonedElement, scope)` where:
 *
 *      * `clonedElement` - is a clone of the original `element` passed into the compiler.
 *      * `scope` - is the current scope with which the linking function is working with.
 *
 *  * `options` - An optional object hash with linking options. If `options` is provided, then the following
 *  keys may be used to control linking behavior:
 *
 *      * `parentBoundTranscludeFn` - the transclude function made available to
 *        directives; if given, it will be passed through to the link functions of
 *        directives found in `element` during compilation.
 *      * `transcludeControllers` - an object hash with keys that map controller names
 *        to a hash with the key `instance`, which maps to the controller instance;
 *        if given, it will make the controllers available to directives on the compileNode:
 *        ```
 *        {
 *          parent: {
 *            instance: parentControllerInstance
 *          }
 *        }
 *        ```
 *      * `futureParentElement` - defines the parent to which the `cloneAttachFn` will add
 *        the cloned elements; only needed for transcludes that are allowed to contain non html
 *        elements (e.g. SVG elements). See also the directive.controller property.
 *
 * Calling the linking function returns the element of the template. It is either the original
 * element passed in, or the clone of the element if the `cloneAttachFn` is provided.
 *
 * After linking the view is not updated until after a call to $digest which typically is done by
 * Angular automatically.
 *
 * If you need access to the bound view, there are two ways to do it:
 *
 * - If you are not asking the linking function to clone the template, create the DOM element(s)
 *   before you send them to the compiler and keep this reference around.
 *   ```js
 *     var element = $compile('<p>{{total}}</p>')(scope);
 *   ```
 *
 * - if on the other hand, you need the element to be cloned, the view reference from the original
 *   example would not point to the clone, but rather to the original template that was cloned. In
 *   this case, you can access the clone via the cloneAttachFn:
 *   ```js
 *     var templateElement = angular.element('<p>{{total}}</p>'),
 *         scope = ....;
 *
 *     var clonedElement = $compile(templateElement)(scope, function(clonedElement, scope) {
 *       //attach the clone to DOM document at the right place
 *     });
 *
 *     //now we have reference to the cloned DOM via `clonedElement`
 *   ```
 *
 *
 * For information on how the compiler works, see the
 * {@link guide/compiler Angular HTML Compiler} section of the Developer Guide.
 *
 * @knownIssue
 *
 * ### Double Compilation
 *
   Double compilation occurs when an already compiled part of the DOM gets
   compiled again. This is an undesired effect and can lead to misbehaving directives, performance issues,
   and memory leaks. Refer to the Compiler Guide {@link guide/compiler#double-compilation-and-how-to-avoid-it
   section on double compilation} for an in-depth explanation and ways to avoid it.
 *
 */

var $compileMinErr = minErr('$compile');

function UNINITIALIZED_VALUE() {}
var _UNINITIALIZED_VALUE = new UNINITIALIZED_VALUE();

/**
 * @ngdoc provider
 * @name $compileProvider
 *
 * @description
 */
$CompileProvider.$inject = ['$provide', '$$sanitizeUriProvider'];
/** @this */
function $CompileProvider($provide, $$sanitizeUriProvider) {
  var hasDirectives = {},
      Suffix = 'Directive',
      COMMENT_DIRECTIVE_REGEXP = /^\s*directive:\s*([\w-]+)\s+(.*)$/,
      CLASS_DIRECTIVE_REGEXP = /(([\w-]+)(?::([^;]+))?;?)/,
      ALL_OR_NOTHING_ATTRS = makeMap('ngSrc,ngSrcset,src,srcset'),
      REQUIRE_PREFIX_REGEXP = /^(?:(\^\^?)?(\?)?(\^\^?)?)?/;

  // Ref: http://developers.whatwg.org/webappapis.html#event-handler-idl-attributes
  // The assumption is that future DOM event attribute names will begin with
  // 'on' and be composed of only English letters.
  var EVENT_HANDLER_ATTR_REGEXP = /^(on[a-z]+|formaction)$/;
  var bindingCache = createMap();

  function parseIsolateBindings(scope, directiveName, isController) {
    var LOCAL_REGEXP = /^\s*([@&<]|=(\*?))(\??)\s*([\w$]*)\s*$/;

    var bindings = createMap();

    forEach(scope, function(definition, scopeName) {
      if (definition in bindingCache) {
        bindings[scopeName] = bindingCache[definition];
        return;
      }
      var match = definition.match(LOCAL_REGEXP);

      if (!match) {
        throw $compileMinErr('iscp',
            'Invalid {3} for directive \'{0}\'.' +
            ' Definition: {... {1}: \'{2}\' ...}',
            directiveName, scopeName, definition,
            (isController ? 'controller bindings definition' :
            'isolate scope definition'));
      }

      bindings[scopeName] = {
        mode: match[1][0],
        collection: match[2] === '*',
        optional: match[3] === '?',
        attrName: match[4] || scopeName
      };
      if (match[4]) {
        bindingCache[definition] = bindings[scopeName];
      }
    });

    return bindings;
  }

  function parseDirectiveBindings(directive, directiveName) {
    var bindings = {
      isolateScope: null,
      bindToController: null
    };
    if (isObject(directive.scope)) {
      if (directive.bindToController === true) {
        bindings.bindToController = parseIsolateBindings(directive.scope,
                                                         directiveName, true);
        bindings.isolateScope = {};
      } else {
        bindings.isolateScope = parseIsolateBindings(directive.scope,
                                                     directiveName, false);
      }
    }
    if (isObject(directive.bindToController)) {
      bindings.bindToController =
          parseIsolateBindings(directive.bindToController, directiveName, true);
    }
    if (bindings.bindToController && !directive.controller) {
      // There is no controller
      throw $compileMinErr('noctrl',
            'Cannot bind to controller without directive \'{0}\'s controller.',
            directiveName);
    }
    return bindings;
  }

  function assertValidDirectiveName(name) {
    var letter = name.charAt(0);
    if (!letter || letter !== lowercase(letter)) {
      throw $compileMinErr('baddir', 'Directive/Component name \'{0}\' is invalid. The first character must be a lowercase letter', name);
    }
    if (name !== name.trim()) {
      throw $compileMinErr('baddir',
            'Directive/Component name \'{0}\' is invalid. The name should not contain leading or trailing whitespaces',
            name);
    }
  }

  function getDirectiveRequire(directive) {
    var require = directive.require || (directive.controller && directive.name);

    if (!isArray(require) && isObject(require)) {
      forEach(require, function(value, key) {
        var match = value.match(REQUIRE_PREFIX_REGEXP);
        var name = value.substring(match[0].length);
        if (!name) require[key] = match[0] + key;
      });
    }

    return require;
  }

  function getDirectiveRestrict(restrict, name) {
    if (restrict && !(isString(restrict) && /[EACM]/.test(restrict))) {
      throw $compileMinErr('badrestrict',
          'Restrict property \'{0}\' of directive \'{1}\' is invalid',
          restrict,
          name);
    }

    return restrict || 'EA';
  }

  /**
   * @ngdoc method
   * @name $compileProvider#directive
   * @kind function
   *
   * @description
   * Register a new directive with the compiler.
   *
   * @param {string|Object} name Name of the directive in camel-case (i.e. <code>ngBind</code> which
   *    will match as <code>ng-bind</code>), or an object map of directives where the keys are the
   *    names and the values are the factories.
   * @param {Function|Array} directiveFactory An injectable directive factory function. See the
   *    {@link guide/directive directive guide} and the {@link $compile compile API} for more info.
   * @returns {ng.$compileProvider} Self for chaining.
   */
  this.directive = function registerDirective(name, directiveFactory) {
    assertArg(name, 'name');
    assertNotHasOwnProperty(name, 'directive');
    if (isString(name)) {
      assertValidDirectiveName(name);
      assertArg(directiveFactory, 'directiveFactory');
      if (!hasDirectives.hasOwnProperty(name)) {
        hasDirectives[name] = [];
        $provide.factory(name + Suffix, ['$injector', '$exceptionHandler',
          function($injector, $exceptionHandler) {
            var directives = [];
            forEach(hasDirectives[name], function(directiveFactory, index) {
              try {
                var directive = $injector.invoke(directiveFactory);
                if (isFunction(directive)) {
                  directive = { compile: valueFn(directive) };
                } else if (!directive.compile && directive.link) {
                  directive.compile = valueFn(directive.link);
                }
                directive.priority = directive.priority || 0;
                directive.index = index;
                directive.name = directive.name || name;
                directive.require = getDirectiveRequire(directive);
                directive.restrict = getDirectiveRestrict(directive.restrict, name);
                directive.$$moduleName = directiveFactory.$$moduleName;
                directives.push(directive);
              } catch (e) {
                $exceptionHandler(e);
              }
            });
            return directives;
          }]);
      }
      hasDirectives[name].push(directiveFactory);
    } else {
      forEach(name, reverseParams(registerDirective));
    }
    return this;
  };

  /**
   * @ngdoc method
   * @name $compileProvider#component
   * @module ng
   * @param {string} name Name of the component in camelCase (i.e. `myComp` which will match `<my-comp>`)
   * @param {Object} options Component definition object (a simplified
   *    {@link ng.$compile#directive-definition-object directive definition object}),
   *    with the following properties (all optional):
   *
   *    - `controller` – `{(string|function()=}` – controller constructor function that should be
   *      associated with newly created scope or the name of a {@link ng.$compile#-controller-
   *      registered controller} if passed as a string. An empty `noop` function by default.
   *    - `controllerAs` – `{string=}` – identifier name for to reference the controller in the component's scope.
   *      If present, the controller will be published to scope under the `controllerAs` name.
   *      If not present, this will default to be `$ctrl`.
   *    - `template` – `{string=|function()=}` – html template as a string or a function that
   *      returns an html template as a string which should be used as the contents of this component.
   *      Empty string by default.
   *
   *      If `template` is a function, then it is {@link auto.$injector#invoke injected} with
   *      the following locals:
   *
   *      - `$element` - Current element
   *      - `$attrs` - Current attributes object for the element
   *
   *    - `templateUrl` – `{string=|function()=}` – path or function that returns a path to an html
   *      template that should be used  as the contents of this component.
   *
   *      If `templateUrl` is a function, then it is {@link auto.$injector#invoke injected} with
   *      the following locals:
   *
   *      - `$element` - Current element
   *      - `$attrs` - Current attributes object for the element
   *
   *    - `bindings` – `{object=}` – defines bindings between DOM attributes and component properties.
   *      Component properties are always bound to the component controller and not to the scope.
   *      See {@link ng.$compile#-bindtocontroller- `bindToController`}.
   *    - `transclude` – `{boolean=}` – whether {@link $compile#transclusion content transclusion} is enabled.
   *      Disabled by default.
   *    - `require` - `{Object<string, string>=}` - requires the controllers of other directives and binds them to
   *      this component's controller. The object keys specify the property names under which the required
   *      controllers (object values) will be bound. See {@link ng.$compile#-require- `require`}.
   *    - `$...` – additional properties to attach to the directive factory function and the controller
   *      constructor function. (This is used by the component router to annotate)
   *
   * @returns {ng.$compileProvider} the compile provider itself, for chaining of function calls.
   * @description
   * Register a **component definition** with the compiler. This is a shorthand for registering a special
   * type of directive, which represents a self-contained UI component in your application. Such components
   * are always isolated (i.e. `scope: {}`) and are always restricted to elements (i.e. `restrict: 'E'`).
   *
   * Component definitions are very simple and do not require as much configuration as defining general
   * directives. Component definitions usually consist only of a template and a controller backing it.
   *
   * In order to make the definition easier, components enforce best practices like use of `controllerAs`,
   * `bindToController`. They always have **isolate scope** and are restricted to elements.
   *
   * Here are a few examples of how you would usually define components:
   *
   * ```js
   *   var myMod = angular.module(...);
   *   myMod.component('myComp', {
   *     template: '<div>My name is {{$ctrl.name}}</div>',
   *     controller: function() {
   *       this.name = 'shahar';
   *     }
   *   });
   *
   *   myMod.component('myComp', {
   *     template: '<div>My name is {{$ctrl.name}}</div>',
   *     bindings: {name: '@'}
   *   });
   *
   *   myMod.component('myComp', {
   *     templateUrl: 'views/my-comp.html',
   *     controller: 'MyCtrl',
   *     controllerAs: 'ctrl',
   *     bindings: {name: '@'}
   *   });
   *
   * ```
   * For more examples, and an in-depth guide, see the {@link guide/component component guide}.
   *
   * <br />
   * See also {@link ng.$compileProvider#directive $compileProvider.directive()}.
   */
  this.component = function registerComponent(name, options) {
    var controller = options.controller || function() {};

    function factory($injector) {
      function makeInjectable(fn) {
        if (isFunction(fn) || isArray(fn)) {
          return /** @this */ function(tElement, tAttrs) {
            return $injector.invoke(fn, this, {$element: tElement, $attrs: tAttrs});
          };
        } else {
          return fn;
        }
      }

      var template = (!options.template && !options.templateUrl ? '' : options.template);
      var ddo = {
        controller: controller,
        controllerAs: identifierForController(options.controller) || options.controllerAs || '$ctrl',
        template: makeInjectable(template),
        templateUrl: makeInjectable(options.templateUrl),
        transclude: options.transclude,
        scope: {},
        bindToController: options.bindings || {},
        restrict: 'E',
        require: options.require
      };

      // Copy annotations (starting with $) over to the DDO
      forEach(options, function(val, key) {
        if (key.charAt(0) === '$') ddo[key] = val;
      });

      return ddo;
    }

    // TODO(pete) remove the following `forEach` before we release 1.6.0
    // The component-router@0.2.0 looks for the annotations on the controller constructor
    // Nothing in Angular looks for annotations on the factory function but we can't remove
    // it from 1.5.x yet.

    // Copy any annotation properties (starting with $) over to the factory and controller constructor functions
    // These could be used by libraries such as the new component router
    forEach(options, function(val, key) {
      if (key.charAt(0) === '$') {
        factory[key] = val;
        // Don't try to copy over annotations to named controller
        if (isFunction(controller)) controller[key] = val;
      }
    });

    factory.$inject = ['$injector'];

    return this.directive(name, factory);
  };


  /**
   * @ngdoc method
   * @name $compileProvider#aHrefSanitizationWhitelist
   * @kind function
   *
   * @description
   * Retrieves or overrides the default regular expression that is used for whitelisting of safe
   * urls during a[href] sanitization.
   *
   * The sanitization is a security measure aimed at preventing XSS attacks via html links.
   *
   * Any url about to be assigned to a[href] via data-binding is first normalized and turned into
   * an absolute url. Afterwards, the url is matched against the `aHrefSanitizationWhitelist`
   * regular expression. If a match is found, the original url is written into the dom. Otherwise,
   * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
   *
   * @param {RegExp=} regexp New regexp to whitelist urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.aHrefSanitizationWhitelist = function(regexp) {
    if (isDefined(regexp)) {
      $$sanitizeUriProvider.aHrefSanitizationWhitelist(regexp);
      return this;
    } else {
      return $$sanitizeUriProvider.aHrefSanitizationWhitelist();
    }
  };


  /**
   * @ngdoc method
   * @name $compileProvider#imgSrcSanitizationWhitelist
   * @kind function
   *
   * @description
   * Retrieves or overrides the default regular expression that is used for whitelisting of safe
   * urls during img[src] sanitization.
   *
   * The sanitization is a security measure aimed at prevent XSS attacks via html links.
   *
   * Any url about to be assigned to img[src] via data-binding is first normalized and turned into
   * an absolute url. Afterwards, the url is matched against the `imgSrcSanitizationWhitelist`
   * regular expression. If a match is found, the original url is written into the dom. Otherwise,
   * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
   *
   * @param {RegExp=} regexp New regexp to whitelist urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.imgSrcSanitizationWhitelist = function(regexp) {
    if (isDefined(regexp)) {
      $$sanitizeUriProvider.imgSrcSanitizationWhitelist(regexp);
      return this;
    } else {
      return $$sanitizeUriProvider.imgSrcSanitizationWhitelist();
    }
  };

  /**
   * @ngdoc method
   * @name  $compileProvider#debugInfoEnabled
   *
   * @param {boolean=} enabled update the debugInfoEnabled state if provided, otherwise just return the
   * current debugInfoEnabled state
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   *
   * @kind function
   *
   * @description
   * Call this method to enable/disable various debug runtime information in the compiler such as adding
   * binding information and a reference to the current scope on to DOM elements.
   * If enabled, the compiler will add the following to DOM elements that have been bound to the scope
   * * `ng-binding` CSS class
   * * `$binding` data property containing an array of the binding expressions
   *
   * You may want to disable this in production for a significant performance boost. See
   * {@link guide/production#disabling-debug-data Disabling Debug Data} for more.
   *
   * The default value is true.
   */
  var debugInfoEnabled = true;
  this.debugInfoEnabled = function(enabled) {
    if (isDefined(enabled)) {
      debugInfoEnabled = enabled;
      return this;
    }
    return debugInfoEnabled;
  };

  /**
   * @ngdoc method
   * @name  $compileProvider#preAssignBindingsEnabled
   *
   * @param {boolean=} enabled update the preAssignBindingsEnabled state if provided, otherwise just return the
   * current preAssignBindingsEnabled state
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   *
   * @kind function
   *
   * @description
   * Call this method to enable/disable whether directive controllers are assigned bindings before
   * calling the controller's constructor.
   * If enabled (true), the compiler assigns the value of each of the bindings to the
   * properties of the controller object before the constructor of this object is called.
   *
   * If disabled (false), the compiler calls the constructor first before assigning bindings.
   *
   * The default value is true in Angular 1.5.x but will switch to false in Angular 1.6.x.
   */
  var preAssignBindingsEnabled = false;
  this.preAssignBindingsEnabled = function(enabled) {
    if (isDefined(enabled)) {
      preAssignBindingsEnabled = enabled;
      return this;
    }
    return preAssignBindingsEnabled;
  };


  var TTL = 10;
  /**
   * @ngdoc method
   * @name $compileProvider#onChangesTtl
   * @description
   *
   * Sets the number of times `$onChanges` hooks can trigger new changes before giving up and
   * assuming that the model is unstable.
   *
   * The current default is 10 iterations.
   *
   * In complex applications it's possible that dependencies between `$onChanges` hooks and bindings will result
   * in several iterations of calls to these hooks. However if an application needs more than the default 10
   * iterations to stabilize then you should investigate what is causing the model to continuously change during
   * the `$onChanges` hook execution.
   *
   * Increasing the TTL could have performance implications, so you should not change it without proper justification.
   *
   * @param {number} limit The number of `$onChanges` hook iterations.
   * @returns {number|object} the current limit (or `this` if called as a setter for chaining)
   */
  this.onChangesTtl = function(value) {
    if (arguments.length) {
      TTL = value;
      return this;
    }
    return TTL;
  };

  var commentDirectivesEnabledConfig = true;
  /**
   * @ngdoc method
   * @name $compileProvider#commentDirectivesEnabled
   * @description
   *
   * It indicates to the compiler
   * whether or not directives on comments should be compiled.
   * Defaults to `true`.
   *
   * Calling this function with false disables the compilation of directives
   * on comments for the whole application.
   * This results in a compilation performance gain,
   * as the compiler doesn't have to check comments when looking for directives.
   * This should however only be used if you are sure that no comment directives are used in
   * the application (including any 3rd party directives).
   *
   * @param {boolean} enabled `false` if the compiler may ignore directives on comments
   * @returns {boolean|object} the current value (or `this` if called as a setter for chaining)
   */
  this.commentDirectivesEnabled = function(value) {
    if (arguments.length) {
      commentDirectivesEnabledConfig = value;
      return this;
    }
    return commentDirectivesEnabledConfig;
  };


  var cssClassDirectivesEnabledConfig = true;
  /**
   * @ngdoc method
   * @name $compileProvider#cssClassDirectivesEnabled
   * @description
   *
   * It indicates to the compiler
   * whether or not directives on element classes should be compiled.
   * Defaults to `true`.
   *
   * Calling this function with false disables the compilation of directives
   * on element classes for the whole application.
   * This results in a compilation performance gain,
   * as the compiler doesn't have to check element classes when looking for directives.
   * This should however only be used if you are sure that no class directives are used in
   * the application (including any 3rd party directives).
   *
   * @param {boolean} enabled `false` if the compiler may ignore directives on element classes
   * @returns {boolean|object} the current value (or `this` if called as a setter for chaining)
   */
  this.cssClassDirectivesEnabled = function(value) {
    if (arguments.length) {
      cssClassDirectivesEnabledConfig = value;
      return this;
    }
    return cssClassDirectivesEnabledConfig;
  };

  this.$get = [
            '$injector', '$interpolate', '$exceptionHandler', '$templateRequest', '$parse',
            '$controller', '$rootScope', '$sce', '$animate', '$$sanitizeUri',
    function($injector,   $interpolate,   $exceptionHandler,   $templateRequest,   $parse,
             $controller,   $rootScope,   $sce,   $animate,   $$sanitizeUri) {

    var SIMPLE_ATTR_NAME = /^\w/;
    var specialAttrHolder = window.document.createElement('div');


    var commentDirectivesEnabled = commentDirectivesEnabledConfig;
    var cssClassDirectivesEnabled = cssClassDirectivesEnabledConfig;


    var onChangesTtl = TTL;
    // The onChanges hooks should all be run together in a single digest
    // When changes occur, the call to trigger their hooks will be added to this queue
    var onChangesQueue;

    // This function is called in a $$postDigest to trigger all the onChanges hooks in a single digest
    function flushOnChangesQueue() {
      try {
        if (!(--onChangesTtl)) {
          // We have hit the TTL limit so reset everything
          onChangesQueue = undefined;
          throw $compileMinErr('infchng', '{0} $onChanges() iterations reached. Aborting!\n', TTL);
        }
        // We must run this hook in an apply since the $$postDigest runs outside apply
        $rootScope.$apply(function() {
          var errors = [];
          for (var i = 0, ii = onChangesQueue.length; i < ii; ++i) {
            try {
              onChangesQueue[i]();
            } catch (e) {
              errors.push(e);
            }
          }
          // Reset the queue to trigger a new schedule next time there is a change
          onChangesQueue = undefined;
          if (errors.length) {
            throw errors;
          }
        });
      } finally {
        onChangesTtl++;
      }
    }


    function Attributes(element, attributesToCopy) {
      if (attributesToCopy) {
        var keys = Object.keys(attributesToCopy);
        var i, l, key;

        for (i = 0, l = keys.length; i < l; i++) {
          key = keys[i];
          this[key] = attributesToCopy[key];
        }
      } else {
        this.$attr = {};
      }

      this.$$element = element;
    }

    Attributes.prototype = {
      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$normalize
       * @kind function
       *
       * @description
       * Converts an attribute name (e.g. dash/colon/underscore-delimited string, optionally prefixed with `x-` or
       * `data-`) to its normalized, camelCase form.
       *
       * Also there is special case for Moz prefix starting with upper case letter.
       *
       * For further information check out the guide on {@link guide/directive#matching-directives Matching Directives}
       *
       * @param {string} name Name to normalize
       */
      $normalize: directiveNormalize,


      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$addClass
       * @kind function
       *
       * @description
       * Adds the CSS class value specified by the classVal parameter to the element. If animations
       * are enabled then an animation will be triggered for the class addition.
       *
       * @param {string} classVal The className value that will be added to the element
       */
      $addClass: function(classVal) {
        if (classVal && classVal.length > 0) {
          $animate.addClass(this.$$element, classVal);
        }
      },

      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$removeClass
       * @kind function
       *
       * @description
       * Removes the CSS class value specified by the classVal parameter from the element. If
       * animations are enabled then an animation will be triggered for the class removal.
       *
       * @param {string} classVal The className value that will be removed from the element
       */
      $removeClass: function(classVal) {
        if (classVal && classVal.length > 0) {
          $animate.removeClass(this.$$element, classVal);
        }
      },

      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$updateClass
       * @kind function
       *
       * @description
       * Adds and removes the appropriate CSS class values to the element based on the difference
       * between the new and old CSS class values (specified as newClasses and oldClasses).
       *
       * @param {string} newClasses The current CSS className value
       * @param {string} oldClasses The former CSS className value
       */
      $updateClass: function(newClasses, oldClasses) {
        var toAdd = tokenDifference(newClasses, oldClasses);
        if (toAdd && toAdd.length) {
          $animate.addClass(this.$$element, toAdd);
        }

        var toRemove = tokenDifference(oldClasses, newClasses);
        if (toRemove && toRemove.length) {
          $animate.removeClass(this.$$element, toRemove);
        }
      },

      /**
       * Set a normalized attribute on the element in a way such that all directives
       * can share the attribute. This function properly handles boolean attributes.
       * @param {string} key Normalized key. (ie ngAttribute)
       * @param {string|boolean} value The value to set. If `null` attribute will be deleted.
       * @param {boolean=} writeAttr If false, does not write the value to DOM element attribute.
       *     Defaults to true.
       * @param {string=} attrName Optional none normalized name. Defaults to key.
       */
      $set: function(key, value, writeAttr, attrName) {
        // TODO: decide whether or not to throw an error if "class"
        //is set through this function since it may cause $updateClass to
        //become unstable.

        var node = this.$$element[0],
            booleanKey = getBooleanAttrName(node, key),
            aliasedKey = getAliasedAttrName(key),
            observer = key,
            nodeName;

        if (booleanKey) {
          this.$$element.prop(key, value);
          attrName = booleanKey;
        } else if (aliasedKey) {
          this[aliasedKey] = value;
          observer = aliasedKey;
        }

        this[key] = value;

        // translate normalized key to actual key
        if (attrName) {
          this.$attr[key] = attrName;
        } else {
          attrName = this.$attr[key];
          if (!attrName) {
            this.$attr[key] = attrName = snake_case(key, '-');
          }
        }

        nodeName = nodeName_(this.$$element);

        if ((nodeName === 'a' && (key === 'href' || key === 'xlinkHref')) ||
            (nodeName === 'img' && key === 'src')) {
          // sanitize a[href] and img[src] values
          this[key] = value = $$sanitizeUri(value, key === 'src');
        } else if (nodeName === 'img' && key === 'srcset' && isDefined(value)) {
          // sanitize img[srcset] values
          var result = '';

          // first check if there are spaces because it's not the same pattern
          var trimmedSrcset = trim(value);
          //                (   999x   ,|   999w   ,|   ,|,   )
          var srcPattern = /(\s+\d+x\s*,|\s+\d+w\s*,|\s+,|,\s+)/;
          var pattern = /\s/.test(trimmedSrcset) ? srcPattern : /(,)/;

          // split srcset into tuple of uri and descriptor except for the last item
          var rawUris = trimmedSrcset.split(pattern);

          // for each tuples
          var nbrUrisWith2parts = Math.floor(rawUris.length / 2);
          for (var i = 0; i < nbrUrisWith2parts; i++) {
            var innerIdx = i * 2;
            // sanitize the uri
            result += $$sanitizeUri(trim(rawUris[innerIdx]), true);
            // add the descriptor
            result += (' ' + trim(rawUris[innerIdx + 1]));
          }

          // split the last item into uri and descriptor
          var lastTuple = trim(rawUris[i * 2]).split(/\s/);

          // sanitize the last uri
          result += $$sanitizeUri(trim(lastTuple[0]), true);

          // and add the last descriptor if any
          if (lastTuple.length === 2) {
            result += (' ' + trim(lastTuple[1]));
          }
          this[key] = value = result;
        }

        if (writeAttr !== false) {
          if (value === null || isUndefined(value)) {
            this.$$element.removeAttr(attrName);
          } else {
            if (SIMPLE_ATTR_NAME.test(attrName)) {
              this.$$element.attr(attrName, value);
            } else {
              setSpecialAttr(this.$$element[0], attrName, value);
            }
          }
        }

        // fire observers
        var $$observers = this.$$observers;
        if ($$observers) {
          forEach($$observers[observer], function(fn) {
            try {
              fn(value);
            } catch (e) {
              $exceptionHandler(e);
            }
          });
        }
      },


      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$observe
       * @kind function
       *
       * @description
       * Observes an interpolated attribute.
       *
       * The observer function will be invoked once during the next `$digest` following
       * compilation. The observer is then invoked whenever the interpolated value
       * changes.
       *
       * @param {string} key Normalized key. (ie ngAttribute) .
       * @param {function(interpolatedValue)} fn Function that will be called whenever
                the interpolated value of the attribute changes.
       *        See the {@link guide/interpolation#how-text-and-attribute-bindings-work Interpolation
       *        guide} for more info.
       * @returns {function()} Returns a deregistration function for this observer.
       */
      $observe: function(key, fn) {
        var attrs = this,
            $$observers = (attrs.$$observers || (attrs.$$observers = createMap())),
            listeners = ($$observers[key] || ($$observers[key] = []));

        listeners.push(fn);
        $rootScope.$evalAsync(function() {
          if (!listeners.$$inter && attrs.hasOwnProperty(key) && !isUndefined(attrs[key])) {
            // no one registered attribute interpolation function, so lets call it manually
            fn(attrs[key]);
          }
        });

        return function() {
          arrayRemove(listeners, fn);
        };
      }
    };

    function setSpecialAttr(element, attrName, value) {
      // Attributes names that do not start with letters (such as `(click)`) cannot be set using `setAttribute`
      // so we have to jump through some hoops to get such an attribute
      // https://github.com/angular/angular.js/pull/13318
      specialAttrHolder.innerHTML = '<span ' + attrName + '>';
      var attributes = specialAttrHolder.firstChild.attributes;
      var attribute = attributes[0];
      // We have to remove the attribute from its container element before we can add it to the destination element
      attributes.removeNamedItem(attribute.name);
      attribute.value = value;
      element.attributes.setNamedItem(attribute);
    }

    function safeAddClass($element, className) {
      try {
        $element.addClass(className);
      } catch (e) {
        // ignore, since it means that we are trying to set class on
        // SVG element, where class name is read-only.
      }
    }


    var startSymbol = $interpolate.startSymbol(),
        endSymbol = $interpolate.endSymbol(),
        denormalizeTemplate = (startSymbol === '{{' && endSymbol  === '}}')
            ? identity
            : function denormalizeTemplate(template) {
              return template.replace(/\{\{/g, startSymbol).replace(/}}/g, endSymbol);
        },
        NG_ATTR_BINDING = /^ngAttr[A-Z]/;
    var MULTI_ELEMENT_DIR_RE = /^(.+)Start$/;

    compile.$$addBindingInfo = debugInfoEnabled ? function $$addBindingInfo($element, binding) {
      var bindings = $element.data('$binding') || [];

      if (isArray(binding)) {
        bindings = bindings.concat(binding);
      } else {
        bindings.push(binding);
      }

      $element.data('$binding', bindings);
    } : noop;

    compile.$$addBindingClass = debugInfoEnabled ? function $$addBindingClass($element) {
      safeAddClass($element, 'ng-binding');
    } : noop;

    compile.$$addScopeInfo = debugInfoEnabled ? function $$addScopeInfo($element, scope, isolated, noTemplate) {
      var dataName = isolated ? (noTemplate ? '$isolateScopeNoTemplate' : '$isolateScope') : '$scope';
      $element.data(dataName, scope);
    } : noop;

    compile.$$addScopeClass = debugInfoEnabled ? function $$addScopeClass($element, isolated) {
      safeAddClass($element, isolated ? 'ng-isolate-scope' : 'ng-scope');
    } : noop;

    compile.$$createComment = function(directiveName, comment) {
      var content = '';
      if (debugInfoEnabled) {
        content = ' ' + (directiveName || '') + ': ';
        if (comment) content += comment + ' ';
      }
      return window.document.createComment(content);
    };

    return compile;

    //================================

    function compile($compileNodes, transcludeFn, maxPriority, ignoreDirective,
                        previousCompileContext) {
      if (!($compileNodes instanceof jqLite)) {
        // jquery always rewraps, whereas we need to preserve the original selector so that we can
        // modify it.
        $compileNodes = jqLite($compileNodes);
      }
      var compositeLinkFn =
              compileNodes($compileNodes, transcludeFn, $compileNodes,
                           maxPriority, ignoreDirective, previousCompileContext);
      compile.$$addScopeClass($compileNodes);
      var namespace = null;
      return function publicLinkFn(scope, cloneConnectFn, options) {
        if (!$compileNodes) {
          throw $compileMinErr('multilink', 'This element has already been linked.');
        }
        assertArg(scope, 'scope');

        if (previousCompileContext && previousCompileContext.needsNewScope) {
          // A parent directive did a replace and a directive on this element asked
          // for transclusion, which caused us to lose a layer of element on which
          // we could hold the new transclusion scope, so we will create it manually
          // here.
          scope = scope.$parent.$new();
        }

        options = options || {};
        var parentBoundTranscludeFn = options.parentBoundTranscludeFn,
          transcludeControllers = options.transcludeControllers,
          futureParentElement = options.futureParentElement;

        // When `parentBoundTranscludeFn` is passed, it is a
        // `controllersBoundTransclude` function (it was previously passed
        // as `transclude` to directive.link) so we must unwrap it to get
        // its `boundTranscludeFn`
        if (parentBoundTranscludeFn && parentBoundTranscludeFn.$$boundTransclude) {
          parentBoundTranscludeFn = parentBoundTranscludeFn.$$boundTransclude;
        }

        if (!namespace) {
          namespace = detectNamespaceForChildElements(futureParentElement);
        }
        var $linkNode;
        if (namespace !== 'html') {
          // When using a directive with replace:true and templateUrl the $compileNodes
          // (or a child element inside of them)
          // might change, so we need to recreate the namespace adapted compileNodes
          // for call to the link function.
          // Note: This will already clone the nodes...
          $linkNode = jqLite(
            wrapTemplate(namespace, jqLite('<div>').append($compileNodes).html())
          );
        } else if (cloneConnectFn) {
          // important!!: we must call our jqLite.clone() since the jQuery one is trying to be smart
          // and sometimes changes the structure of the DOM.
          $linkNode = JQLitePrototype.clone.call($compileNodes);
        } else {
          $linkNode = $compileNodes;
        }

        if (transcludeControllers) {
          for (var controllerName in transcludeControllers) {
            $linkNode.data('$' + controllerName + 'Controller', transcludeControllers[controllerName].instance);
          }
        }

        compile.$$addScopeInfo($linkNode, scope);

        if (cloneConnectFn) cloneConnectFn($linkNode, scope);
        if (compositeLinkFn) compositeLinkFn(scope, $linkNode, $linkNode, parentBoundTranscludeFn);

        if (!cloneConnectFn) {
          $compileNodes = compositeLinkFn = null;
        }
        return $linkNode;
      };
    }

    function detectNamespaceForChildElements(parentElement) {
      // TODO: Make this detect MathML as well...
      var node = parentElement && parentElement[0];
      if (!node) {
        return 'html';
      } else {
        return nodeName_(node) !== 'foreignobject' && toString.call(node).match(/SVG/) ? 'svg' : 'html';
      }
    }

    /**
     * Compile function matches each node in nodeList against the directives. Once all directives
     * for a particular node are collected their compile functions are executed. The compile
     * functions return values - the linking functions - are combined into a composite linking
     * function, which is the a linking function for the node.
     *
     * @param {NodeList} nodeList an array of nodes or NodeList to compile
     * @param {function(angular.Scope, cloneAttachFn=)} transcludeFn A linking function, where the
     *        scope argument is auto-generated to the new child of the transcluded parent scope.
     * @param {DOMElement=} $rootElement If the nodeList is the root of the compilation tree then
     *        the rootElement must be set the jqLite collection of the compile root. This is
     *        needed so that the jqLite collection items can be replaced with widgets.
     * @param {number=} maxPriority Max directive priority.
     * @returns {Function} A composite linking function of all of the matched directives or null.
     */
    function compileNodes(nodeList, transcludeFn, $rootElement, maxPriority, ignoreDirective,
                            previousCompileContext) {
      var linkFns = [],
          // `nodeList` can be either an element's `.childNodes` (live NodeList)
          // or a jqLite/jQuery collection or an array
          notLiveList = isArray(nodeList) || (nodeList instanceof jqLite),
          attrs, directives, nodeLinkFn, childNodes, childLinkFn, linkFnFound, nodeLinkFnFound;


      for (var i = 0; i < nodeList.length; i++) {
        attrs = new Attributes();

        // Support: IE 11 only
        // Workaround for #11781 and #14924
        if (msie === 11) {
          mergeConsecutiveTextNodes(nodeList, i, notLiveList);
        }

        // We must always refer to `nodeList[i]` hereafter,
        // since the nodes can be replaced underneath us.
        directives = collectDirectives(nodeList[i], [], attrs, i === 0 ? maxPriority : undefined,
                                        ignoreDirective);

        nodeLinkFn = (directives.length)
            ? applyDirectivesToNode(directives, nodeList[i], attrs, transcludeFn, $rootElement,
                                      null, [], [], previousCompileContext)
            : null;

        if (nodeLinkFn && nodeLinkFn.scope) {
          compile.$$addScopeClass(attrs.$$element);
        }

        childLinkFn = (nodeLinkFn && nodeLinkFn.terminal ||
                      !(childNodes = nodeList[i].childNodes) ||
                      !childNodes.length)
            ? null
            : compileNodes(childNodes,
                 nodeLinkFn ? (
                  (nodeLinkFn.transcludeOnThisElement || !nodeLinkFn.templateOnThisElement)
                     && nodeLinkFn.transclude) : transcludeFn);

        if (nodeLinkFn || childLinkFn) {
          linkFns.push(i, nodeLinkFn, childLinkFn);
          linkFnFound = true;
          nodeLinkFnFound = nodeLinkFnFound || nodeLinkFn;
        }

        //use the previous context only for the first element in the virtual group
        previousCompileContext = null;
      }

      // return a linking function if we have found anything, null otherwise
      return linkFnFound ? compositeLinkFn : null;

      function compositeLinkFn(scope, nodeList, $rootElement, parentBoundTranscludeFn) {
        var nodeLinkFn, childLinkFn, node, childScope, i, ii, idx, childBoundTranscludeFn;
        var stableNodeList;


        if (nodeLinkFnFound) {
          // copy nodeList so that if a nodeLinkFn removes or adds an element at this DOM level our
          // offsets don't get screwed up
          var nodeListLength = nodeList.length;
          stableNodeList = new Array(nodeListLength);

          // create a sparse array by only copying the elements which have a linkFn
          for (i = 0; i < linkFns.length; i += 3) {
            idx = linkFns[i];
            stableNodeList[idx] = nodeList[idx];
          }
        } else {
          stableNodeList = nodeList;
        }

        for (i = 0, ii = linkFns.length; i < ii;) {
          node = stableNodeList[linkFns[i++]];
          nodeLinkFn = linkFns[i++];
          childLinkFn = linkFns[i++];

          if (nodeLinkFn) {
            if (nodeLinkFn.scope) {
              childScope = scope.$new();
              compile.$$addScopeInfo(jqLite(node), childScope);
            } else {
              childScope = scope;
            }

            if (nodeLinkFn.transcludeOnThisElement) {
              childBoundTranscludeFn = createBoundTranscludeFn(
                  scope, nodeLinkFn.transclude, parentBoundTranscludeFn);

            } else if (!nodeLinkFn.templateOnThisElement && parentBoundTranscludeFn) {
              childBoundTranscludeFn = parentBoundTranscludeFn;

            } else if (!parentBoundTranscludeFn && transcludeFn) {
              childBoundTranscludeFn = createBoundTranscludeFn(scope, transcludeFn);

            } else {
              childBoundTranscludeFn = null;
            }

            nodeLinkFn(childLinkFn, childScope, node, $rootElement, childBoundTranscludeFn);

          } else if (childLinkFn) {
            childLinkFn(scope, node.childNodes, undefined, parentBoundTranscludeFn);
          }
        }
      }
    }

    function mergeConsecutiveTextNodes(nodeList, idx, notLiveList) {
      var node = nodeList[idx];
      var parent = node.parentNode;
      var sibling;

      if (node.nodeType !== NODE_TYPE_TEXT) {
        return;
      }

      while (true) {
        sibling = parent ? node.nextSibling : nodeList[idx + 1];
        if (!sibling || sibling.nodeType !== NODE_TYPE_TEXT) {
          break;
        }

        node.nodeValue = node.nodeValue + sibling.nodeValue;

        if (sibling.parentNode) {
          sibling.parentNode.removeChild(sibling);
        }
        if (notLiveList && sibling === nodeList[idx + 1]) {
          nodeList.splice(idx + 1, 1);
        }
      }
    }

    function createBoundTranscludeFn(scope, transcludeFn, previousBoundTranscludeFn) {
      function boundTranscludeFn(transcludedScope, cloneFn, controllers, futureParentElement, containingScope) {

        if (!transcludedScope) {
          transcludedScope = scope.$new(false, containingScope);
          transcludedScope.$$transcluded = true;
        }

        return transcludeFn(transcludedScope, cloneFn, {
          parentBoundTranscludeFn: previousBoundTranscludeFn,
          transcludeControllers: controllers,
          futureParentElement: futureParentElement
        });
      }

      // We need  to attach the transclusion slots onto the `boundTranscludeFn`
      // so that they are available inside the `controllersBoundTransclude` function
      var boundSlots = boundTranscludeFn.$$slots = createMap();
      for (var slotName in transcludeFn.$$slots) {
        if (transcludeFn.$$slots[slotName]) {
          boundSlots[slotName] = createBoundTranscludeFn(scope, transcludeFn.$$slots[slotName], previousBoundTranscludeFn);
        } else {
          boundSlots[slotName] = null;
        }
      }

      return boundTranscludeFn;
    }

    /**
     * Looks for directives on the given node and adds them to the directive collection which is
     * sorted.
     *
     * @param node Node to search.
     * @param directives An array to which the directives are added to. This array is sorted before
     *        the function returns.
     * @param attrs The shared attrs object which is used to populate the normalized attributes.
     * @param {number=} maxPriority Max directive priority.
     */
    function collectDirectives(node, directives, attrs, maxPriority, ignoreDirective) {
      var nodeType = node.nodeType,
          attrsMap = attrs.$attr,
          match,
          nodeName,
          className;

      switch (nodeType) {
        case NODE_TYPE_ELEMENT: /* Element */

          nodeName = nodeName_(node);

          // use the node name: <directive>
          addDirective(directives,
              directiveNormalize(nodeName), 'E', maxPriority, ignoreDirective);

          // iterate over the attributes
          for (var attr, name, nName, ngAttrName, value, isNgAttr, nAttrs = node.attributes,
                   j = 0, jj = nAttrs && nAttrs.length; j < jj; j++) {
            var attrStartName = false;
            var attrEndName = false;

            attr = nAttrs[j];
            name = attr.name;
            value = attr.value;

            // support ngAttr attribute binding
            ngAttrName = directiveNormalize(name);
            isNgAttr = NG_ATTR_BINDING.test(ngAttrName);
            if (isNgAttr) {
              name = name.replace(PREFIX_REGEXP, '')
                .substr(8).replace(/_(.)/g, function(match, letter) {
                  return letter.toUpperCase();
                });
            }

            var multiElementMatch = ngAttrName.match(MULTI_ELEMENT_DIR_RE);
            if (multiElementMatch && directiveIsMultiElement(multiElementMatch[1])) {
              attrStartName = name;
              attrEndName = name.substr(0, name.length - 5) + 'end';
              name = name.substr(0, name.length - 6);
            }

            nName = directiveNormalize(name.toLowerCase());
            attrsMap[nName] = name;
            if (isNgAttr || !attrs.hasOwnProperty(nName)) {
                attrs[nName] = value;
                if (getBooleanAttrName(node, nName)) {
                  attrs[nName] = true; // presence means true
                }
            }
            addAttrInterpolateDirective(node, directives, value, nName, isNgAttr);
            addDirective(directives, nName, 'A', maxPriority, ignoreDirective, attrStartName,
                          attrEndName);
          }

          if (nodeName === 'input' && node.getAttribute('type') === 'hidden') {
            // Hidden input elements can have strange behaviour when navigating back to the page
            // This tells the browser not to try to cache and reinstate previous values
            node.setAttribute('autocomplete', 'off');
          }

          // use class as directive
          if (!cssClassDirectivesEnabled) break;
          className = node.className;
          if (isObject(className)) {
              // Maybe SVGAnimatedString
              className = className.animVal;
          }
          if (isString(className) && className !== '') {
            while ((match = CLASS_DIRECTIVE_REGEXP.exec(className))) {
              nName = directiveNormalize(match[2]);
              if (addDirective(directives, nName, 'C', maxPriority, ignoreDirective)) {
                attrs[nName] = trim(match[3]);
              }
              className = className.substr(match.index + match[0].length);
            }
          }
          break;
        case NODE_TYPE_TEXT: /* Text Node */
          addTextInterpolateDirective(directives, node.nodeValue);
          break;
        case NODE_TYPE_COMMENT: /* Comment */
          if (!commentDirectivesEnabled) break;
          collectCommentDirectives(node, directives, attrs, maxPriority, ignoreDirective);
          break;
      }

      directives.sort(byPriority);
      return directives;
    }

    function collectCommentDirectives(node, directives, attrs, maxPriority, ignoreDirective) {
      // function created because of performance, try/catch disables
      // the optimization of the whole function #14848
      try {
        var match = COMMENT_DIRECTIVE_REGEXP.exec(node.nodeValue);
        if (match) {
          var nName = directiveNormalize(match[1]);
          if (addDirective(directives, nName, 'M', maxPriority, ignoreDirective)) {
            attrs[nName] = trim(match[2]);
          }
        }
      } catch (e) {
        // turns out that under some circumstances IE9 throws errors when one attempts to read
        // comment's node value.
        // Just ignore it and continue. (Can't seem to reproduce in test case.)
      }
    }

    /**
     * Given a node with a directive-start it collects all of the siblings until it finds
     * directive-end.
     * @param node
     * @param attrStart
     * @param attrEnd
     * @returns {*}
     */
    function groupScan(node, attrStart, attrEnd) {
      var nodes = [];
      var depth = 0;
      if (attrStart && node.hasAttribute && node.hasAttribute(attrStart)) {
        do {
          if (!node) {
            throw $compileMinErr('uterdir',
                      'Unterminated attribute, found \'{0}\' but no matching \'{1}\' found.',
                      attrStart, attrEnd);
          }
          if (node.nodeType === NODE_TYPE_ELEMENT) {
            if (node.hasAttribute(attrStart)) depth++;
            if (node.hasAttribute(attrEnd)) depth--;
          }
          nodes.push(node);
          node = node.nextSibling;
        } while (depth > 0);
      } else {
        nodes.push(node);
      }

      return jqLite(nodes);
    }

    /**
     * Wrapper for linking function which converts normal linking function into a grouped
     * linking function.
     * @param linkFn
     * @param attrStart
     * @param attrEnd
     * @returns {Function}
     */
    function groupElementsLinkFnWrapper(linkFn, attrStart, attrEnd) {
      return function groupedElementsLink(scope, element, attrs, controllers, transcludeFn) {
        element = groupScan(element[0], attrStart, attrEnd);
        return linkFn(scope, element, attrs, controllers, transcludeFn);
      };
    }

    /**
     * A function generator that is used to support both eager and lazy compilation
     * linking function.
     * @param eager
     * @param $compileNodes
     * @param transcludeFn
     * @param maxPriority
     * @param ignoreDirective
     * @param previousCompileContext
     * @returns {Function}
     */
    function compilationGenerator(eager, $compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext) {
      var compiled;

      if (eager) {
        return compile($compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext);
      }
      return /** @this */ function lazyCompilation() {
        if (!compiled) {
          compiled = compile($compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext);

          // Null out all of these references in order to make them eligible for garbage collection
          // since this is a potentially long lived closure
          $compileNodes = transcludeFn = previousCompileContext = null;
        }
        return compiled.apply(this, arguments);
      };
    }

    /**
     * Once the directives have been collected, their compile functions are executed. This method
     * is responsible for inlining directive templates as well as terminating the application
     * of the directives if the terminal directive has been reached.
     *
     * @param {Array} directives Array of collected directives to execute their compile function.
     *        this needs to be pre-sorted by priority order.
     * @param {Node} compileNode The raw DOM node to apply the compile functions to
     * @param {Object} templateAttrs The shared attribute function
     * @param {function(angular.Scope, cloneAttachFn=)} transcludeFn A linking function, where the
     *                                                  scope argument is auto-generated to the new
     *                                                  child of the transcluded parent scope.
     * @param {JQLite} jqCollection If we are working on the root of the compile tree then this
     *                              argument has the root jqLite array so that we can replace nodes
     *                              on it.
     * @param {Object=} originalReplaceDirective An optional directive that will be ignored when
     *                                           compiling the transclusion.
     * @param {Array.<Function>} preLinkFns
     * @param {Array.<Function>} postLinkFns
     * @param {Object} previousCompileContext Context used for previous compilation of the current
     *                                        node
     * @returns {Function} linkFn
     */
    function applyDirectivesToNode(directives, compileNode, templateAttrs, transcludeFn,
                                   jqCollection, originalReplaceDirective, preLinkFns, postLinkFns,
                                   previousCompileContext) {
      previousCompileContext = previousCompileContext || {};

      var terminalPriority = -Number.MAX_VALUE,
          newScopeDirective = previousCompileContext.newScopeDirective,
          controllerDirectives = previousCompileContext.controllerDirectives,
          newIsolateScopeDirective = previousCompileContext.newIsolateScopeDirective,
          templateDirective = previousCompileContext.templateDirective,
          nonTlbTranscludeDirective = previousCompileContext.nonTlbTranscludeDirective,
          hasTranscludeDirective = false,
          hasTemplate = false,
          hasElementTranscludeDirective = previousCompileContext.hasElementTranscludeDirective,
          $compileNode = templateAttrs.$$element = jqLite(compileNode),
          directive,
          directiveName,
          $template,
          replaceDirective = originalReplaceDirective,
          childTranscludeFn = transcludeFn,
          linkFn,
          didScanForMultipleTransclusion = false,
          mightHaveMultipleTransclusionError = false,
          directiveValue;

      // executes all directives on the current element
      for (var i = 0, ii = directives.length; i < ii; i++) {
        directive = directives[i];
        var attrStart = directive.$$start;
        var attrEnd = directive.$$end;

        // collect multiblock sections
        if (attrStart) {
          $compileNode = groupScan(compileNode, attrStart, attrEnd);
        }
        $template = undefined;

        if (terminalPriority > directive.priority) {
          break; // prevent further processing of directives
        }

        directiveValue = directive.scope;

        if (directiveValue) {

          // skip the check for directives with async templates, we'll check the derived sync
          // directive when the template arrives
          if (!directive.templateUrl) {
            if (isObject(directiveValue)) {
              // This directive is trying to add an isolated scope.
              // Check that there is no scope of any kind already
              assertNoDuplicate('new/isolated scope', newIsolateScopeDirective || newScopeDirective,
                                directive, $compileNode);
              newIsolateScopeDirective = directive;
            } else {
              // This directive is trying to add a child scope.
              // Check that there is no isolated scope already
              assertNoDuplicate('new/isolated scope', newIsolateScopeDirective, directive,
                                $compileNode);
            }
          }

          newScopeDirective = newScopeDirective || directive;
        }

        directiveName = directive.name;

        // If we encounter a condition that can result in transclusion on the directive,
        // then scan ahead in the remaining directives for others that may cause a multiple
        // transclusion error to be thrown during the compilation process.  If a matching directive
        // is found, then we know that when we encounter a transcluded directive, we need to eagerly
        // compile the `transclude` function rather than doing it lazily in order to throw
        // exceptions at the correct time
        if (!didScanForMultipleTransclusion && ((directive.replace && (directive.templateUrl || directive.template))
            || (directive.transclude && !directive.$$tlb))) {
                var candidateDirective;

                for (var scanningIndex = i + 1; (candidateDirective = directives[scanningIndex++]);) {
                    if ((candidateDirective.transclude && !candidateDirective.$$tlb)
                        || (candidateDirective.replace && (candidateDirective.templateUrl || candidateDirective.template))) {
                        mightHaveMultipleTransclusionError = true;
                        break;
                    }
                }

                didScanForMultipleTransclusion = true;
        }

        if (!directive.templateUrl && directive.controller) {
          controllerDirectives = controllerDirectives || createMap();
          assertNoDuplicate('\'' + directiveName + '\' controller',
              controllerDirectives[directiveName], directive, $compileNode);
          controllerDirectives[directiveName] = directive;
        }

        directiveValue = directive.transclude;

        if (directiveValue) {
          hasTranscludeDirective = true;

          // Special case ngIf and ngRepeat so that we don't complain about duplicate transclusion.
          // This option should only be used by directives that know how to safely handle element transclusion,
          // where the transcluded nodes are added or replaced after linking.
          if (!directive.$$tlb) {
            assertNoDuplicate('transclusion', nonTlbTranscludeDirective, directive, $compileNode);
            nonTlbTranscludeDirective = directive;
          }

          if (directiveValue === 'element') {
            hasElementTranscludeDirective = true;
            terminalPriority = directive.priority;
            $template = $compileNode;
            $compileNode = templateAttrs.$$element =
                jqLite(compile.$$createComment(directiveName, templateAttrs[directiveName]));
            compileNode = $compileNode[0];
            replaceWith(jqCollection, sliceArgs($template), compileNode);

            // Support: Chrome < 50
            // https://github.com/angular/angular.js/issues/14041

            // In the versions of V8 prior to Chrome 50, the document fragment that is created
            // in the `replaceWith` function is improperly garbage collected despite still
            // being referenced by the `parentNode` property of all of the child nodes.  By adding
            // a reference to the fragment via a different property, we can avoid that incorrect
            // behavior.
            // TODO: remove this line after Chrome 50 has been released
            $template[0].$$parentNode = $template[0].parentNode;

            childTranscludeFn = compilationGenerator(mightHaveMultipleTransclusionError, $template, transcludeFn, terminalPriority,
                                        replaceDirective && replaceDirective.name, {
                                          // Don't pass in:
                                          // - controllerDirectives - otherwise we'll create duplicates controllers
                                          // - newIsolateScopeDirective or templateDirective - combining templates with
                                          //   element transclusion doesn't make sense.
                                          //
                                          // We need only nonTlbTranscludeDirective so that we prevent putting transclusion
                                          // on the same element more than once.
                                          nonTlbTranscludeDirective: nonTlbTranscludeDirective
                                        });
          } else {

            var slots = createMap();

            if (!isObject(directiveValue)) {
              $template = jqLite(jqLiteClone(compileNode)).contents();
            } else {

              // We have transclusion slots,
              // collect them up, compile them and store their transclusion functions
              $template = [];

              var slotMap = createMap();
              var filledSlots = createMap();

              // Parse the element selectors
              forEach(directiveValue, function(elementSelector, slotName) {
                // If an element selector starts with a ? then it is optional
                var optional = (elementSelector.charAt(0) === '?');
                elementSelector = optional ? elementSelector.substring(1) : elementSelector;

                slotMap[elementSelector] = slotName;

                // We explicitly assign `null` since this implies that a slot was defined but not filled.
                // Later when calling boundTransclusion functions with a slot name we only error if the
                // slot is `undefined`
                slots[slotName] = null;

                // filledSlots contains `true` for all slots that are either optional or have been
                // filled. This is used to check that we have not missed any required slots
                filledSlots[slotName] = optional;
              });

              // Add the matching elements into their slot
              forEach($compileNode.contents(), function(node) {
                var slotName = slotMap[directiveNormalize(nodeName_(node))];
                if (slotName) {
                  filledSlots[slotName] = true;
                  slots[slotName] = slots[slotName] || [];
                  slots[slotName].push(node);
                } else {
                  $template.push(node);
                }
              });

              // Check for required slots that were not filled
              forEach(filledSlots, function(filled, slotName) {
                if (!filled) {
                  throw $compileMinErr('reqslot', 'Required transclusion slot `{0}` was not filled.', slotName);
                }
              });

              for (var slotName in slots) {
                if (slots[slotName]) {
                  // Only define a transclusion function if the slot was filled
                  slots[slotName] = compilationGenerator(mightHaveMultipleTransclusionError, slots[slotName], transcludeFn);
                }
              }
            }

            $compileNode.empty(); // clear contents
            childTranscludeFn = compilationGenerator(mightHaveMultipleTransclusionError, $template, transcludeFn, undefined,
                undefined, { needsNewScope: directive.$$isolateScope || directive.$$newScope});
            childTranscludeFn.$$slots = slots;
          }
        }

        if (directive.template) {
          hasTemplate = true;
          assertNoDuplicate('template', templateDirective, directive, $compileNode);
          templateDirective = directive;

          directiveValue = (isFunction(directive.template))
              ? directive.template($compileNode, templateAttrs)
              : directive.template;

          directiveValue = denormalizeTemplate(directiveValue);

          if (directive.replace) {
            replaceDirective = directive;
            if (jqLiteIsTextNode(directiveValue)) {
              $template = [];
            } else {
              $template = removeComments(wrapTemplate(directive.templateNamespace, trim(directiveValue)));
            }
            compileNode = $template[0];

            if ($template.length !== 1 || compileNode.nodeType !== NODE_TYPE_ELEMENT) {
              throw $compileMinErr('tplrt',
                  'Template for directive \'{0}\' must have exactly one root element. {1}',
                  directiveName, '');
            }

            replaceWith(jqCollection, $compileNode, compileNode);

            var newTemplateAttrs = {$attr: {}};

            // combine directives from the original node and from the template:
            // - take the array of directives for this element
            // - split it into two parts, those that already applied (processed) and those that weren't (unprocessed)
            // - collect directives from the template and sort them by priority
            // - combine directives as: processed + template + unprocessed
            var templateDirectives = collectDirectives(compileNode, [], newTemplateAttrs);
            var unprocessedDirectives = directives.splice(i + 1, directives.length - (i + 1));

            if (newIsolateScopeDirective || newScopeDirective) {
              // The original directive caused the current element to be replaced but this element
              // also needs to have a new scope, so we need to tell the template directives
              // that they would need to get their scope from further up, if they require transclusion
              markDirectiveScope(templateDirectives, newIsolateScopeDirective, newScopeDirective);
            }
            directives = directives.concat(templateDirectives).concat(unprocessedDirectives);
            mergeTemplateAttributes(templateAttrs, newTemplateAttrs);

            ii = directives.length;
          } else {
            $compileNode.html(directiveValue);
          }
        }

        if (directive.templateUrl) {
          hasTemplate = true;
          assertNoDuplicate('template', templateDirective, directive, $compileNode);
          templateDirective = directive;

          if (directive.replace) {
            replaceDirective = directive;
          }

          // eslint-disable-next-line no-func-assign
          nodeLinkFn = compileTemplateUrl(directives.splice(i, directives.length - i), $compileNode,
              templateAttrs, jqCollection, hasTranscludeDirective && childTranscludeFn, preLinkFns, postLinkFns, {
                controllerDirectives: controllerDirectives,
                newScopeDirective: (newScopeDirective !== directive) && newScopeDirective,
                newIsolateScopeDirective: newIsolateScopeDirective,
                templateDirective: templateDirective,
                nonTlbTranscludeDirective: nonTlbTranscludeDirective
              });
          ii = directives.length;
        } else if (directive.compile) {
          try {
            linkFn = directive.compile($compileNode, templateAttrs, childTranscludeFn);
            var context = directive.$$originalDirective || directive;
            if (isFunction(linkFn)) {
              addLinkFns(null, bind(context, linkFn), attrStart, attrEnd);
            } else if (linkFn) {
              addLinkFns(bind(context, linkFn.pre), bind(context, linkFn.post), attrStart, attrEnd);
            }
          } catch (e) {
            $exceptionHandler(e, startingTag($compileNode));
          }
        }

        if (directive.terminal) {
          nodeLinkFn.terminal = true;
          terminalPriority = Math.max(terminalPriority, directive.priority);
        }

      }

      nodeLinkFn.scope = newScopeDirective && newScopeDirective.scope === true;
      nodeLinkFn.transcludeOnThisElement = hasTranscludeDirective;
      nodeLinkFn.templateOnThisElement = hasTemplate;
      nodeLinkFn.transclude = childTranscludeFn;

      previousCompileContext.hasElementTranscludeDirective = hasElementTranscludeDirective;

      // might be normal or delayed nodeLinkFn depending on if templateUrl is present
      return nodeLinkFn;

      ////////////////////

      function addLinkFns(pre, post, attrStart, attrEnd) {
        if (pre) {
          if (attrStart) pre = groupElementsLinkFnWrapper(pre, attrStart, attrEnd);
          pre.require = directive.require;
          pre.directiveName = directiveName;
          if (newIsolateScopeDirective === directive || directive.$$isolateScope) {
            pre = cloneAndAnnotateFn(pre, {isolateScope: true});
          }
          preLinkFns.push(pre);
        }
        if (post) {
          if (attrStart) post = groupElementsLinkFnWrapper(post, attrStart, attrEnd);
          post.require = directive.require;
          post.directiveName = directiveName;
          if (newIsolateScopeDirective === directive || directive.$$isolateScope) {
            post = cloneAndAnnotateFn(post, {isolateScope: true});
          }
          postLinkFns.push(post);
        }
      }

      function nodeLinkFn(childLinkFn, scope, linkNode, $rootElement, boundTranscludeFn) {
        var i, ii, linkFn, isolateScope, controllerScope, elementControllers, transcludeFn, $element,
            attrs, scopeBindingInfo;

        if (compileNode === linkNode) {
          attrs = templateAttrs;
          $element = templateAttrs.$$element;
        } else {
          $element = jqLite(linkNode);
          attrs = new Attributes($element, templateAttrs);
        }

        controllerScope = scope;
        if (newIsolateScopeDirective) {
          isolateScope = scope.$new(true);
        } else if (newScopeDirective) {
          controllerScope = scope.$parent;
        }

        if (boundTranscludeFn) {
          // track `boundTranscludeFn` so it can be unwrapped if `transcludeFn`
          // is later passed as `parentBoundTranscludeFn` to `publicLinkFn`
          transcludeFn = controllersBoundTransclude;
          transcludeFn.$$boundTransclude = boundTranscludeFn;
          // expose the slots on the `$transclude` function
          transcludeFn.isSlotFilled = function(slotName) {
            return !!boundTranscludeFn.$$slots[slotName];
          };
        }

        if (controllerDirectives) {
          elementControllers = setupControllers($element, attrs, transcludeFn, controllerDirectives, isolateScope, scope, newIsolateScopeDirective);
        }

        if (newIsolateScopeDirective) {
          // Initialize isolate scope bindings for new isolate scope directive.
          compile.$$addScopeInfo($element, isolateScope, true, !(templateDirective && (templateDirective === newIsolateScopeDirective ||
              templateDirective === newIsolateScopeDirective.$$originalDirective)));
          compile.$$addScopeClass($element, true);
          isolateScope.$$isolateBindings =
              newIsolateScopeDirective.$$isolateBindings;
          scopeBindingInfo = initializeDirectiveBindings(scope, attrs, isolateScope,
                                        isolateScope.$$isolateBindings,
                                        newIsolateScopeDirective);
          if (scopeBindingInfo.removeWatches) {
            isolateScope.$on('$destroy', scopeBindingInfo.removeWatches);
          }
        }

        // Initialize bindToController bindings
        for (var name in elementControllers) {
          var controllerDirective = controllerDirectives[name];
          var controller = elementControllers[name];
          var bindings = controllerDirective.$$bindings.bindToController;

          if (preAssignBindingsEnabled) {
            if (bindings) {
              controller.bindingInfo =
                initializeDirectiveBindings(controllerScope, attrs, controller.instance, bindings, controllerDirective);
            } else {
              controller.bindingInfo = {};
            }

            var controllerResult = controller();
            if (controllerResult !== controller.instance) {
              // If the controller constructor has a return value, overwrite the instance
              // from setupControllers
              controller.instance = controllerResult;
              $element.data('$' + controllerDirective.name + 'Controller', controllerResult);
              if (controller.bindingInfo.removeWatches) {
                controller.bindingInfo.removeWatches();
              }
              controller.bindingInfo =
                initializeDirectiveBindings(controllerScope, attrs, controller.instance, bindings, controllerDirective);
            }
          } else {
            controller.instance = controller();
            $element.data('$' + controllerDirective.name + 'Controller', controller.instance);
            controller.bindingInfo =
              initializeDirectiveBindings(controllerScope, attrs, controller.instance, bindings, controllerDirective);
          }
        }

        // Bind the required controllers to the controller, if `require` is an object and `bindToController` is truthy
        forEach(controllerDirectives, function(controllerDirective, name) {
          var require = controllerDirective.require;
          if (controllerDirective.bindToController && !isArray(require) && isObject(require)) {
            extend(elementControllers[name].instance, getControllers(name, require, $element, elementControllers));
          }
        });

        // Handle the init and destroy lifecycle hooks on all controllers that have them
        forEach(elementControllers, function(controller) {
          var controllerInstance = controller.instance;
          if (isFunction(controllerInstance.$onChanges)) {
            try {
              controllerInstance.$onChanges(controller.bindingInfo.initialChanges);
            } catch (e) {
              $exceptionHandler(e);
            }
          }
          if (isFunction(controllerInstance.$onInit)) {
            try {
              controllerInstance.$onInit();
            } catch (e) {
              $exceptionHandler(e);
            }
          }
          if (isFunction(controllerInstance.$doCheck)) {
            controllerScope.$watch(function() { controllerInstance.$doCheck(); });
            controllerInstance.$doCheck();
          }
          if (isFunction(controllerInstance.$onDestroy)) {
            controllerScope.$on('$destroy', function callOnDestroyHook() {
              controllerInstance.$onDestroy();
            });
          }
        });

        // PRELINKING
        for (i = 0, ii = preLinkFns.length; i < ii; i++) {
          linkFn = preLinkFns[i];
          invokeLinkFn(linkFn,
              linkFn.isolateScope ? isolateScope : scope,
              $element,
              attrs,
              linkFn.require && getControllers(linkFn.directiveName, linkFn.require, $element, elementControllers),
              transcludeFn
          );
        }

        // RECURSION
        // We only pass the isolate scope, if the isolate directive has a template,
        // otherwise the child elements do not belong to the isolate directive.
        var scopeToChild = scope;
        if (newIsolateScopeDirective && (newIsolateScopeDirective.template || newIsolateScopeDirective.templateUrl === null)) {
          scopeToChild = isolateScope;
        }
        if (childLinkFn) {
          childLinkFn(scopeToChild, linkNode.childNodes, undefined, boundTranscludeFn);
        }

        // POSTLINKING
        for (i = postLinkFns.length - 1; i >= 0; i--) {
          linkFn = postLinkFns[i];
          invokeLinkFn(linkFn,
              linkFn.isolateScope ? isolateScope : scope,
              $element,
              attrs,
              linkFn.require && getControllers(linkFn.directiveName, linkFn.require, $element, elementControllers),
              transcludeFn
          );
        }

        // Trigger $postLink lifecycle hooks
        forEach(elementControllers, function(controller) {
          var controllerInstance = controller.instance;
          if (isFunction(controllerInstance.$postLink)) {
            controllerInstance.$postLink();
          }
        });

        // This is the function that is injected as `$transclude`.
        // Note: all arguments are optional!
        function controllersBoundTransclude(scope, cloneAttachFn, futureParentElement, slotName) {
          var transcludeControllers;
          // No scope passed in:
          if (!isScope(scope)) {
            slotName = futureParentElement;
            futureParentElement = cloneAttachFn;
            cloneAttachFn = scope;
            scope = undefined;
          }

          if (hasElementTranscludeDirective) {
            transcludeControllers = elementControllers;
          }
          if (!futureParentElement) {
            futureParentElement = hasElementTranscludeDirective ? $element.parent() : $element;
          }
          if (slotName) {
            // slotTranscludeFn can be one of three things:
            //  * a transclude function - a filled slot
            //  * `null` - an optional slot that was not filled
            //  * `undefined` - a slot that was not declared (i.e. invalid)
            var slotTranscludeFn = boundTranscludeFn.$$slots[slotName];
            if (slotTranscludeFn) {
              return slotTranscludeFn(scope, cloneAttachFn, transcludeControllers, futureParentElement, scopeToChild);
            } else if (isUndefined(slotTranscludeFn)) {
              throw $compileMinErr('noslot',
               'No parent directive that requires a transclusion with slot name "{0}". ' +
               'Element: {1}',
               slotName, startingTag($element));
            }
          } else {
            return boundTranscludeFn(scope, cloneAttachFn, transcludeControllers, futureParentElement, scopeToChild);
          }
        }
      }
    }

    function getControllers(directiveName, require, $element, elementControllers) {
      var value;

      if (isString(require)) {
        var match = require.match(REQUIRE_PREFIX_REGEXP);
        var name = require.substring(match[0].length);
        var inheritType = match[1] || match[3];
        var optional = match[2] === '?';

        //If only parents then start at the parent element
        if (inheritType === '^^') {
          $element = $element.parent();
        //Otherwise attempt getting the controller from elementControllers in case
        //the element is transcluded (and has no data) and to avoid .data if possible
        } else {
          value = elementControllers && elementControllers[name];
          value = value && value.instance;
        }

        if (!value) {
          var dataName = '$' + name + 'Controller';
          value = inheritType ? $element.inheritedData(dataName) : $element.data(dataName);
        }

        if (!value && !optional) {
          throw $compileMinErr('ctreq',
              'Controller \'{0}\', required by directive \'{1}\', can\'t be found!',
              name, directiveName);
        }
      } else if (isArray(require)) {
        value = [];
        for (var i = 0, ii = require.length; i < ii; i++) {
          value[i] = getControllers(directiveName, require[i], $element, elementControllers);
        }
      } else if (isObject(require)) {
        value = {};
        forEach(require, function(controller, property) {
          value[property] = getControllers(directiveName, controller, $element, elementControllers);
        });
      }

      return value || null;
    }

    function setupControllers($element, attrs, transcludeFn, controllerDirectives, isolateScope, scope, newIsolateScopeDirective) {
      var elementControllers = createMap();
      for (var controllerKey in controllerDirectives) {
        var directive = controllerDirectives[controllerKey];
        var locals = {
          $scope: directive === newIsolateScopeDirective || directive.$$isolateScope ? isolateScope : scope,
          $element: $element,
          $attrs: attrs,
          $transclude: transcludeFn
        };

        var controller = directive.controller;
        if (controller === '@') {
          controller = attrs[directive.name];
        }

        var controllerInstance = $controller(controller, locals, true, directive.controllerAs);

        // For directives with element transclusion the element is a comment.
        // In this case .data will not attach any data.
        // Instead, we save the controllers for the element in a local hash and attach to .data
        // later, once we have the actual element.
        elementControllers[directive.name] = controllerInstance;
        $element.data('$' + directive.name + 'Controller', controllerInstance.instance);
      }
      return elementControllers;
    }

    // Depending upon the context in which a directive finds itself it might need to have a new isolated
    // or child scope created. For instance:
    // * if the directive has been pulled into a template because another directive with a higher priority
    // asked for element transclusion
    // * if the directive itself asks for transclusion but it is at the root of a template and the original
    // element was replaced. See https://github.com/angular/angular.js/issues/12936
    function markDirectiveScope(directives, isolateScope, newScope) {
      for (var j = 0, jj = directives.length; j < jj; j++) {
        directives[j] = inherit(directives[j], {$$isolateScope: isolateScope, $$newScope: newScope});
      }
    }

    /**
     * looks up the directive and decorates it with exception handling and proper parameters. We
     * call this the boundDirective.
     *
     * @param {string} name name of the directive to look up.
     * @param {string} location The directive must be found in specific format.
     *   String containing any of theses characters:
     *
     *   * `E`: element name
     *   * `A': attribute
     *   * `C`: class
     *   * `M`: comment
     * @returns {boolean} true if directive was added.
     */
    function addDirective(tDirectives, name, location, maxPriority, ignoreDirective, startAttrName,
                          endAttrName) {
      if (name === ignoreDirective) return null;
      var match = null;
      if (hasDirectives.hasOwnProperty(name)) {
        for (var directive, directives = $injector.get(name + Suffix),
            i = 0, ii = directives.length; i < ii; i++) {
          directive = directives[i];
          if ((isUndefined(maxPriority) || maxPriority > directive.priority) &&
               directive.restrict.indexOf(location) !== -1) {
            if (startAttrName) {
              directive = inherit(directive, {$$start: startAttrName, $$end: endAttrName});
            }
            if (!directive.$$bindings) {
              var bindings = directive.$$bindings =
                  parseDirectiveBindings(directive, directive.name);
              if (isObject(bindings.isolateScope)) {
                directive.$$isolateBindings = bindings.isolateScope;
              }
            }
            tDirectives.push(directive);
            match = directive;
          }
        }
      }
      return match;
    }


    /**
     * looks up the directive and returns true if it is a multi-element directive,
     * and therefore requires DOM nodes between -start and -end markers to be grouped
     * together.
     *
     * @param {string} name name of the directive to look up.
     * @returns true if directive was registered as multi-element.
     */
    function directiveIsMultiElement(name) {
      if (hasDirectives.hasOwnProperty(name)) {
        for (var directive, directives = $injector.get(name + Suffix),
            i = 0, ii = directives.length; i < ii; i++) {
          directive = directives[i];
          if (directive.multiElement) {
            return true;
          }
        }
      }
      return false;
    }

    /**
     * When the element is replaced with HTML template then the new attributes
     * on the template need to be merged with the existing attributes in the DOM.
     * The desired effect is to have both of the attributes present.
     *
     * @param {object} dst destination attributes (original DOM)
     * @param {object} src source attributes (from the directive template)
     */
    function mergeTemplateAttributes(dst, src) {
      var srcAttr = src.$attr,
          dstAttr = dst.$attr;

      // reapply the old attributes to the new element
      forEach(dst, function(value, key) {
        if (key.charAt(0) !== '$') {
          if (src[key] && src[key] !== value) {
            if (value.length) {
              value += (key === 'style' ? ';' : ' ') + src[key];
            } else {
              value = src[key];
            }
          }
          dst.$set(key, value, true, srcAttr[key]);
        }
      });

      // copy the new attributes on the old attrs object
      forEach(src, function(value, key) {
        // Check if we already set this attribute in the loop above.
        // `dst` will never contain hasOwnProperty as DOM parser won't let it.
        // You will get an "InvalidCharacterError: DOM Exception 5" error if you
        // have an attribute like "has-own-property" or "data-has-own-property", etc.
        if (!dst.hasOwnProperty(key) && key.charAt(0) !== '$') {
          dst[key] = value;

          if (key !== 'class' && key !== 'style') {
            dstAttr[key] = srcAttr[key];
          }
        }
      });
    }


    function compileTemplateUrl(directives, $compileNode, tAttrs,
        $rootElement, childTranscludeFn, preLinkFns, postLinkFns, previousCompileContext) {
      var linkQueue = [],
          afterTemplateNodeLinkFn,
          afterTemplateChildLinkFn,
          beforeTemplateCompileNode = $compileNode[0],
          origAsyncDirective = directives.shift(),
          derivedSyncDirective = inherit(origAsyncDirective, {
            templateUrl: null, transclude: null, replace: null, $$originalDirective: origAsyncDirective
          }),
          templateUrl = (isFunction(origAsyncDirective.templateUrl))
              ? origAsyncDirective.templateUrl($compileNode, tAttrs)
              : origAsyncDirective.templateUrl,
          templateNamespace = origAsyncDirective.templateNamespace;

      $compileNode.empty();

      $templateRequest(templateUrl)
        .then(function(content) {
          var compileNode, tempTemplateAttrs, $template, childBoundTranscludeFn;

          content = denormalizeTemplate(content);

          if (origAsyncDirective.replace) {
            if (jqLiteIsTextNode(content)) {
              $template = [];
            } else {
              $template = removeComments(wrapTemplate(templateNamespace, trim(content)));
            }
            compileNode = $template[0];

            if ($template.length !== 1 || compileNode.nodeType !== NODE_TYPE_ELEMENT) {
              throw $compileMinErr('tplrt',
                  'Template for directive \'{0}\' must have exactly one root element. {1}',
                  origAsyncDirective.name, templateUrl);
            }

            tempTemplateAttrs = {$attr: {}};
            replaceWith($rootElement, $compileNode, compileNode);
            var templateDirectives = collectDirectives(compileNode, [], tempTemplateAttrs);

            if (isObject(origAsyncDirective.scope)) {
              // the original directive that caused the template to be loaded async required
              // an isolate scope
              markDirectiveScope(templateDirectives, true);
            }
            directives = templateDirectives.concat(directives);
            mergeTemplateAttributes(tAttrs, tempTemplateAttrs);
          } else {
            compileNode = beforeTemplateCompileNode;
            $compileNode.html(content);
          }

          directives.unshift(derivedSyncDirective);

          afterTemplateNodeLinkFn = applyDirectivesToNode(directives, compileNode, tAttrs,
              childTranscludeFn, $compileNode, origAsyncDirective, preLinkFns, postLinkFns,
              previousCompileContext);
          forEach($rootElement, function(node, i) {
            if (node === compileNode) {
              $rootElement[i] = $compileNode[0];
            }
          });
          afterTemplateChildLinkFn = compileNodes($compileNode[0].childNodes, childTranscludeFn);

          while (linkQueue.length) {
            var scope = linkQueue.shift(),
                beforeTemplateLinkNode = linkQueue.shift(),
                linkRootElement = linkQueue.shift(),
                boundTranscludeFn = linkQueue.shift(),
                linkNode = $compileNode[0];

            if (scope.$$destroyed) continue;

            if (beforeTemplateLinkNode !== beforeTemplateCompileNode) {
              var oldClasses = beforeTemplateLinkNode.className;

              if (!(previousCompileContext.hasElementTranscludeDirective &&
                  origAsyncDirective.replace)) {
                // it was cloned therefore we have to clone as well.
                linkNode = jqLiteClone(compileNode);
              }
              replaceWith(linkRootElement, jqLite(beforeTemplateLinkNode), linkNode);

              // Copy in CSS classes from original node
              safeAddClass(jqLite(linkNode), oldClasses);
            }
            if (afterTemplateNodeLinkFn.transcludeOnThisElement) {
              childBoundTranscludeFn = createBoundTranscludeFn(scope, afterTemplateNodeLinkFn.transclude, boundTranscludeFn);
            } else {
              childBoundTranscludeFn = boundTranscludeFn;
            }
            afterTemplateNodeLinkFn(afterTemplateChildLinkFn, scope, linkNode, $rootElement,
              childBoundTranscludeFn);
          }
          linkQueue = null;
        }).catch(function(error) {
          if (error instanceof Error) {
            $exceptionHandler(error);
          }
        });

      return function delayedNodeLinkFn(ignoreChildLinkFn, scope, node, rootElement, boundTranscludeFn) {
        var childBoundTranscludeFn = boundTranscludeFn;
        if (scope.$$destroyed) return;
        if (linkQueue) {
          linkQueue.push(scope,
                         node,
                         rootElement,
                         childBoundTranscludeFn);
        } else {
          if (afterTemplateNodeLinkFn.transcludeOnThisElement) {
            childBoundTranscludeFn = createBoundTranscludeFn(scope, afterTemplateNodeLinkFn.transclude, boundTranscludeFn);
          }
          afterTemplateNodeLinkFn(afterTemplateChildLinkFn, scope, node, rootElement, childBoundTranscludeFn);
        }
      };
    }


    /**
     * Sorting function for bound directives.
     */
    function byPriority(a, b) {
      var diff = b.priority - a.priority;
      if (diff !== 0) return diff;
      if (a.name !== b.name) return (a.name < b.name) ? -1 : 1;
      return a.index - b.index;
    }

    function assertNoDuplicate(what, previousDirective, directive, element) {

      function wrapModuleNameIfDefined(moduleName) {
        return moduleName ?
          (' (module: ' + moduleName + ')') :
          '';
      }

      if (previousDirective) {
        throw $compileMinErr('multidir', 'Multiple directives [{0}{1}, {2}{3}] asking for {4} on: {5}',
            previousDirective.name, wrapModuleNameIfDefined(previousDirective.$$moduleName),
            directive.name, wrapModuleNameIfDefined(directive.$$moduleName), what, startingTag(element));
      }
    }


    function addTextInterpolateDirective(directives, text) {
      var interpolateFn = $interpolate(text, true);
      if (interpolateFn) {
        directives.push({
          priority: 0,
          compile: function textInterpolateCompileFn(templateNode) {
            var templateNodeParent = templateNode.parent(),
                hasCompileParent = !!templateNodeParent.length;

            // When transcluding a template that has bindings in the root
            // we don't have a parent and thus need to add the class during linking fn.
            if (hasCompileParent) compile.$$addBindingClass(templateNodeParent);

            return function textInterpolateLinkFn(scope, node) {
              var parent = node.parent();
              if (!hasCompileParent) compile.$$addBindingClass(parent);
              compile.$$addBindingInfo(parent, interpolateFn.expressions);
              scope.$watch(interpolateFn, function interpolateFnWatchAction(value) {
                node[0].nodeValue = value;
              });
            };
          }
        });
      }
    }


    function wrapTemplate(type, template) {
      type = lowercase(type || 'html');
      switch (type) {
      case 'svg':
      case 'math':
        var wrapper = window.document.createElement('div');
        wrapper.innerHTML = '<' + type + '>' + template + '</' + type + '>';
        return wrapper.childNodes[0].childNodes;
      default:
        return template;
      }
    }


    function getTrustedContext(node, attrNormalizedName) {
      if (attrNormalizedName === 'srcdoc') {
        return $sce.HTML;
      }
      var tag = nodeName_(node);
      // All tags with src attributes require a RESOURCE_URL value, except for
      // img and various html5 media tags.
      if (attrNormalizedName === 'src' || attrNormalizedName === 'ngSrc') {
        if (['img', 'video', 'audio', 'source', 'track'].indexOf(tag) === -1) {
          return $sce.RESOURCE_URL;
        }
      // maction[xlink:href] can source SVG.  It's not limited to <maction>.
      } else if (attrNormalizedName === 'xlinkHref' ||
          (tag === 'form' && attrNormalizedName === 'action') ||
          // links can be stylesheets or imports, which can run script in the current origin
          (tag === 'link' && attrNormalizedName === 'href')
      ) {
        return $sce.RESOURCE_URL;
      }
    }


    function addAttrInterpolateDirective(node, directives, value, name, isNgAttr) {
      var trustedContext = getTrustedContext(node, name);
      var mustHaveExpression = !isNgAttr;
      var allOrNothing = ALL_OR_NOTHING_ATTRS[name] || isNgAttr;

      var interpolateFn = $interpolate(value, mustHaveExpression, trustedContext, allOrNothing);

      // no interpolation found -> ignore
      if (!interpolateFn) return;

      if (name === 'multiple' && nodeName_(node) === 'select') {
        throw $compileMinErr('selmulti',
            'Binding to the \'multiple\' attribute is not supported. Element: {0}',
            startingTag(node));
      }

      if (EVENT_HANDLER_ATTR_REGEXP.test(name)) {
        throw $compileMinErr('nodomevents',
            'Interpolations for HTML DOM event attributes are disallowed.  Please use the ' +
                'ng- versions (such as ng-click instead of onclick) instead.');
      }

      directives.push({
        priority: 100,
        compile: function() {
            return {
              pre: function attrInterpolatePreLinkFn(scope, element, attr) {
                var $$observers = (attr.$$observers || (attr.$$observers = createMap()));

                // If the attribute has changed since last $interpolate()ed
                var newValue = attr[name];
                if (newValue !== value) {
                  // we need to interpolate again since the attribute value has been updated
                  // (e.g. by another directive's compile function)
                  // ensure unset/empty values make interpolateFn falsy
                  interpolateFn = newValue && $interpolate(newValue, true, trustedContext, allOrNothing);
                  value = newValue;
                }

                // if attribute was updated so that there is no interpolation going on we don't want to
                // register any observers
                if (!interpolateFn) return;

                // initialize attr object so that it's ready in case we need the value for isolate
                // scope initialization, otherwise the value would not be available from isolate
                // directive's linking fn during linking phase
                attr[name] = interpolateFn(scope);

                ($$observers[name] || ($$observers[name] = [])).$$inter = true;
                (attr.$$observers && attr.$$observers[name].$$scope || scope).
                  $watch(interpolateFn, function interpolateFnWatchAction(newValue, oldValue) {
                    //special case for class attribute addition + removal
                    //so that class changes can tap into the animation
                    //hooks provided by the $animate service. Be sure to
                    //skip animations when the first digest occurs (when
                    //both the new and the old values are the same) since
                    //the CSS classes are the non-interpolated values
                    if (name === 'class' && newValue !== oldValue) {
                      attr.$updateClass(newValue, oldValue);
                    } else {
                      attr.$set(name, newValue);
                    }
                  });
              }
            };
          }
      });
    }


    /**
     * This is a special jqLite.replaceWith, which can replace items which
     * have no parents, provided that the containing jqLite collection is provided.
     *
     * @param {JqLite=} $rootElement The root of the compile tree. Used so that we can replace nodes
     *                               in the root of the tree.
     * @param {JqLite} elementsToRemove The jqLite element which we are going to replace. We keep
     *                                  the shell, but replace its DOM node reference.
     * @param {Node} newNode The new DOM node.
     */
    function replaceWith($rootElement, elementsToRemove, newNode) {
      var firstElementToRemove = elementsToRemove[0],
          removeCount = elementsToRemove.length,
          parent = firstElementToRemove.parentNode,
          i, ii;

      if ($rootElement) {
        for (i = 0, ii = $rootElement.length; i < ii; i++) {
          if ($rootElement[i] === firstElementToRemove) {
            $rootElement[i++] = newNode;
            for (var j = i, j2 = j + removeCount - 1,
                     jj = $rootElement.length;
                 j < jj; j++, j2++) {
              if (j2 < jj) {
                $rootElement[j] = $rootElement[j2];
              } else {
                delete $rootElement[j];
              }
            }
            $rootElement.length -= removeCount - 1;

            // If the replaced element is also the jQuery .context then replace it
            // .context is a deprecated jQuery api, so we should set it only when jQuery set it
            // http://api.jquery.com/context/
            if ($rootElement.context === firstElementToRemove) {
              $rootElement.context = newNode;
            }
            break;
          }
        }
      }

      if (parent) {
        parent.replaceChild(newNode, firstElementToRemove);
      }

      // Append all the `elementsToRemove` to a fragment. This will...
      // - remove them from the DOM
      // - allow them to still be traversed with .nextSibling
      // - allow a single fragment.qSA to fetch all elements being removed
      var fragment = window.document.createDocumentFragment();
      for (i = 0; i < removeCount; i++) {
        fragment.appendChild(elementsToRemove[i]);
      }

      if (jqLite.hasData(firstElementToRemove)) {
        // Copy over user data (that includes Angular's $scope etc.). Don't copy private
        // data here because there's no public interface in jQuery to do that and copying over
        // event listeners (which is the main use of private data) wouldn't work anyway.
        jqLite.data(newNode, jqLite.data(firstElementToRemove));

        // Remove $destroy event listeners from `firstElementToRemove`
        jqLite(firstElementToRemove).off('$destroy');
      }

      // Cleanup any data/listeners on the elements and children.
      // This includes invoking the $destroy event on any elements with listeners.
      jqLite.cleanData(fragment.querySelectorAll('*'));

      // Update the jqLite collection to only contain the `newNode`
      for (i = 1; i < removeCount; i++) {
        delete elementsToRemove[i];
      }
      elementsToRemove[0] = newNode;
      elementsToRemove.length = 1;
    }


    function cloneAndAnnotateFn(fn, annotation) {
      return extend(function() { return fn.apply(null, arguments); }, fn, annotation);
    }


    function invokeLinkFn(linkFn, scope, $element, attrs, controllers, transcludeFn) {
      try {
        linkFn(scope, $element, attrs, controllers, transcludeFn);
      } catch (e) {
        $exceptionHandler(e, startingTag($element));
      }
    }


    // Set up $watches for isolate scope and controller bindings.
    function initializeDirectiveBindings(scope, attrs, destination, bindings, directive) {
      var removeWatchCollection = [];
      var initialChanges = {};
      var changes;
      forEach(bindings, function initializeBinding(definition, scopeName) {
        var attrName = definition.attrName,
        optional = definition.optional,
        mode = definition.mode, // @, =, <, or &
        lastValue,
        parentGet, parentSet, compare, removeWatch;

        switch (mode) {

          case '@':
            if (!optional && !hasOwnProperty.call(attrs, attrName)) {
              destination[scopeName] = attrs[attrName] = undefined;
            }
            removeWatch = attrs.$observe(attrName, function(value) {
              if (isString(value) || isBoolean(value)) {
                var oldValue = destination[scopeName];
                recordChanges(scopeName, value, oldValue);
                destination[scopeName] = value;
              }
            });
            attrs.$$observers[attrName].$$scope = scope;
            lastValue = attrs[attrName];
            if (isString(lastValue)) {
              // If the attribute has been provided then we trigger an interpolation to ensure
              // the value is there for use in the link fn
              destination[scopeName] = $interpolate(lastValue)(scope);
            } else if (isBoolean(lastValue)) {
              // If the attributes is one of the BOOLEAN_ATTR then Angular will have converted
              // the value to boolean rather than a string, so we special case this situation
              destination[scopeName] = lastValue;
            }
            initialChanges[scopeName] = new SimpleChange(_UNINITIALIZED_VALUE, destination[scopeName]);
            removeWatchCollection.push(removeWatch);
            break;

          case '=':
            if (!hasOwnProperty.call(attrs, attrName)) {
              if (optional) break;
              attrs[attrName] = undefined;
            }
            if (optional && !attrs[attrName]) break;

            parentGet = $parse(attrs[attrName]);
            if (parentGet.literal) {
              compare = equals;
            } else {
              // eslint-disable-next-line no-self-compare
              compare = function simpleCompare(a, b) { return a === b || (a !== a && b !== b); };
            }
            parentSet = parentGet.assign || function() {
              // reset the change, or we will throw this exception on every $digest
              lastValue = destination[scopeName] = parentGet(scope);
              throw $compileMinErr('nonassign',
                  'Expression \'{0}\' in attribute \'{1}\' used with directive \'{2}\' is non-assignable!',
                  attrs[attrName], attrName, directive.name);
            };
            lastValue = destination[scopeName] = parentGet(scope);
            var parentValueWatch = function parentValueWatch(parentValue) {
              if (!compare(parentValue, destination[scopeName])) {
                // we are out of sync and need to copy
                if (!compare(parentValue, lastValue)) {
                  // parent changed and it has precedence
                  destination[scopeName] = parentValue;
                } else {
                  // if the parent can be assigned then do so
                  parentSet(scope, parentValue = destination[scopeName]);
                }
              }
              lastValue = parentValue;
              return lastValue;
            };
            parentValueWatch.$stateful = true;
            if (definition.collection) {
              removeWatch = scope.$watchCollection(attrs[attrName], parentValueWatch);
            } else {
              removeWatch = scope.$watch($parse(attrs[attrName], parentValueWatch), null, parentGet.literal);
            }
            removeWatchCollection.push(removeWatch);
            break;

          case '<':
            if (!hasOwnProperty.call(attrs, attrName)) {
              if (optional) break;
              attrs[attrName] = undefined;
            }
            if (optional && !attrs[attrName]) break;

            parentGet = $parse(attrs[attrName]);
            var deepWatch = parentGet.literal;

            var initialValue = destination[scopeName] = parentGet(scope);
            initialChanges[scopeName] = new SimpleChange(_UNINITIALIZED_VALUE, destination[scopeName]);

            removeWatch = scope.$watch(parentGet, function parentValueWatchAction(newValue, oldValue) {
              if (oldValue === newValue) {
                if (oldValue === initialValue || (deepWatch && equals(oldValue, initialValue))) {
                  return;
                }
                oldValue = initialValue;
              }
              recordChanges(scopeName, newValue, oldValue);
              destination[scopeName] = newValue;
            }, deepWatch);

            removeWatchCollection.push(removeWatch);
            break;

          case '&':
            // Don't assign Object.prototype method to scope
            parentGet = attrs.hasOwnProperty(attrName) ? $parse(attrs[attrName]) : noop;

            // Don't assign noop to destination if expression is not valid
            if (parentGet === noop && optional) break;

            destination[scopeName] = function(locals) {
              return parentGet(scope, locals);
            };
            break;
        }
      });

      function recordChanges(key, currentValue, previousValue) {
        if (isFunction(destination.$onChanges) && currentValue !== previousValue &&
            // eslint-disable-next-line no-self-compare
            (currentValue === currentValue || previousValue === previousValue)) {
          // If we have not already scheduled the top level onChangesQueue handler then do so now
          if (!onChangesQueue) {
            scope.$$postDigest(flushOnChangesQueue);
            onChangesQueue = [];
          }
          // If we have not already queued a trigger of onChanges for this controller then do so now
          if (!changes) {
            changes = {};
            onChangesQueue.push(triggerOnChangesHook);
          }
          // If the has been a change on this property already then we need to reuse the previous value
          if (changes[key]) {
            previousValue = changes[key].previousValue;
          }
          // Store this change
          changes[key] = new SimpleChange(previousValue, currentValue);
        }
      }

      function triggerOnChangesHook() {
        destination.$onChanges(changes);
        // Now clear the changes so that we schedule onChanges when more changes arrive
        changes = undefined;
      }

      return {
        initialChanges: initialChanges,
        removeWatches: removeWatchCollection.length && function removeWatches() {
          for (var i = 0, ii = removeWatchCollection.length; i < ii; ++i) {
            removeWatchCollection[i]();
          }
        }
      };
    }
  }];
}

function SimpleChange(previous, current) {
  this.previousValue = previous;
  this.currentValue = current;
}
SimpleChange.prototype.isFirstChange = function() { return this.previousValue === _UNINITIALIZED_VALUE; };


var PREFIX_REGEXP = /^((?:x|data)[:\-_])/i;
var SPECIAL_CHARS_REGEXP = /[:\-_]+(.)/g;

/**
 * Converts all accepted directives format into proper directive name.
 * @param name Name to normalize
 */
function directiveNormalize(name) {
  return name
    .replace(PREFIX_REGEXP, '')
    .replace(SPECIAL_CHARS_REGEXP, fnCamelCaseReplace);
}

/**
 * @ngdoc type
 * @name $compile.directive.Attributes
 *
 * @description
 * A shared object between directive compile / linking functions which contains normalized DOM
 * element attributes. The values reflect current binding state `{{ }}`. The normalization is
 * needed since all of these are treated as equivalent in Angular:
 *
 * ```
 *    <span ng:bind="a" ng-bind="a" data-ng-bind="a" x-ng-bind="a">
 * ```
 */

/**
 * @ngdoc property
 * @name $compile.directive.Attributes#$attr
 *
 * @description
 * A map of DOM element attribute names to the normalized name. This is
 * needed to do reverse lookup from normalized name back to actual name.
 */


/**
 * @ngdoc method
 * @name $compile.directive.Attributes#$set
 * @kind function
 *
 * @description
 * Set DOM element attribute value.
 *
 *
 * @param {string} name Normalized element attribute name of the property to modify. The name is
 *          reverse-translated using the {@link ng.$compile.directive.Attributes#$attr $attr}
 *          property to the original name.
 * @param {string} value Value to set the attribute to. The value can be an interpolated string.
 */



/**
 * Closure compiler type information
 */

function nodesetLinkingFn(
  /* angular.Scope */ scope,
  /* NodeList */ nodeList,
  /* Element */ rootElement,
  /* function(Function) */ boundTranscludeFn
) {}

function directiveLinkingFn(
  /* nodesetLinkingFn */ nodesetLinkingFn,
  /* angular.Scope */ scope,
  /* Node */ node,
  /* Element */ rootElement,
  /* function(Function) */ boundTranscludeFn
) {}

function tokenDifference(str1, str2) {
  var values = '',
      tokens1 = str1.split(/\s+/),
      tokens2 = str2.split(/\s+/);

  outer:
  for (var i = 0; i < tokens1.length; i++) {
    var token = tokens1[i];
    for (var j = 0; j < tokens2.length; j++) {
      if (token === tokens2[j]) continue outer;
    }
    values += (values.length > 0 ? ' ' : '') + token;
  }
  return values;
}

function removeComments(jqNodes) {
  jqNodes = jqLite(jqNodes);
  var i = jqNodes.length;

  if (i <= 1) {
    return jqNodes;
  }

  while (i--) {
    var node = jqNodes[i];
    if (node.nodeType === NODE_TYPE_COMMENT ||
       (node.nodeType === NODE_TYPE_TEXT && node.nodeValue.trim() === '')) {
         splice.call(jqNodes, i, 1);
    }
  }
  return jqNodes;
}

var $controllerMinErr = minErr('$controller');


var CNTRL_REG = /^(\S+)(\s+as\s+([\w$]+))?$/;
function identifierForController(controller, ident) {
  if (ident && isString(ident)) return ident;
  if (isString(controller)) {
    var match = CNTRL_REG.exec(controller);
    if (match) return match[3];
  }
}


/**
 * @ngdoc provider
 * @name $controllerProvider
 * @this
 *
 * @description
 * The {@link ng.$controller $controller service} is used by Angular to create new
 * controllers.
 *
 * This provider allows controller registration via the
 * {@link ng.$controllerProvider#register register} method.
 */
function $ControllerProvider() {
  var controllers = {},
      globals = false;

  /**
   * @ngdoc method
   * @name $controllerProvider#has
   * @param {string} name Controller name to check.
   */
  this.has = function(name) {
    return controllers.hasOwnProperty(name);
  };

  /**
   * @ngdoc method
   * @name $controllerProvider#register
   * @param {string|Object} name Controller name, or an object map of controllers where the keys are
   *    the names and the values are the constructors.
   * @param {Function|Array} constructor Controller constructor fn (optionally decorated with DI
   *    annotations in the array notation).
   */
  this.register = function(name, constructor) {
    assertNotHasOwnProperty(name, 'controller');
    if (isObject(name)) {
      extend(controllers, name);
    } else {
      controllers[name] = constructor;
    }
  };

  /**
   * @ngdoc method
   * @name $controllerProvider#allowGlobals
   * @description If called, allows `$controller` to find controller constructors on `window`
   *
   * @deprecated
   * sinceVersion="v1.3.0"
   * removeVersion="v1.7.0"
   * This method of finding controllers has been deprecated.
   */
  this.allowGlobals = function() {
    globals = true;
  };


  this.$get = ['$injector', '$window', function($injector, $window) {

    /**
     * @ngdoc service
     * @name $controller
     * @requires $injector
     *
     * @param {Function|string} constructor If called with a function then it's considered to be the
     *    controller constructor function. Otherwise it's considered to be a string which is used
     *    to retrieve the controller constructor using the following steps:
     *
     *    * check if a controller with given name is registered via `$controllerProvider`
     *    * check if evaluating the string on the current scope returns a constructor
     *    * if $controllerProvider#allowGlobals, check `window[constructor]` on the global
     *      `window` object (deprecated, not recommended)
     *
     *    The string can use the `controller as property` syntax, where the controller instance is published
     *    as the specified property on the `scope`; the `scope` must be injected into `locals` param for this
     *    to work correctly.
     *
     * @param {Object} locals Injection locals for Controller.
     * @return {Object} Instance of given controller.
     *
     * @description
     * `$controller` service is responsible for instantiating controllers.
     *
     * It's just a simple call to {@link auto.$injector $injector}, but extracted into
     * a service, so that one can override this service with [BC version](https://gist.github.com/1649788).
     */
    return function $controller(expression, locals, later, ident) {
      // PRIVATE API:
      //   param `later` --- indicates that the controller's constructor is invoked at a later time.
      //                     If true, $controller will allocate the object with the correct
      //                     prototype chain, but will not invoke the controller until a returned
      //                     callback is invoked.
      //   param `ident` --- An optional label which overrides the label parsed from the controller
      //                     expression, if any.
      var instance, match, constructor, identifier;
      later = later === true;
      if (ident && isString(ident)) {
        identifier = ident;
      }

      if (isString(expression)) {
        match = expression.match(CNTRL_REG);
        if (!match) {
          throw $controllerMinErr('ctrlfmt',
            'Badly formed controller string \'{0}\'. ' +
            'Must match `__name__ as __id__` or `__name__`.', expression);
        }
        constructor = match[1];
        identifier = identifier || match[3];
        expression = controllers.hasOwnProperty(constructor)
            ? controllers[constructor]
            : getter(locals.$scope, constructor, true) ||
                (globals ? getter($window, constructor, true) : undefined);

        if (!expression) {
          throw $controllerMinErr('ctrlreg',
            'The controller with the name \'{0}\' is not registered.', constructor);
        }

        assertArgFn(expression, constructor, true);
      }

      if (later) {
        // Instantiate controller later:
        // This machinery is used to create an instance of the object before calling the
        // controller's constructor itself.
        //
        // This allows properties to be added to the controller before the constructor is
        // invoked. Primarily, this is used for isolate scope bindings in $compile.
        //
        // This feature is not intended for use by applications, and is thus not documented
        // publicly.
        // Object creation: http://jsperf.com/create-constructor/2
        var controllerPrototype = (isArray(expression) ?
          expression[expression.length - 1] : expression).prototype;
        instance = Object.create(controllerPrototype || null);

        if (identifier) {
          addIdentifier(locals, identifier, instance, constructor || expression.name);
        }

        return extend(function $controllerInit() {
          var result = $injector.invoke(expression, instance, locals, constructor);
          if (result !== instance && (isObject(result) || isFunction(result))) {
            instance = result;
            if (identifier) {
              // If result changed, re-assign controllerAs value to scope.
              addIdentifier(locals, identifier, instance, constructor || expression.name);
            }
          }
          return instance;
        }, {
          instance: instance,
          identifier: identifier
        });
      }

      instance = $injector.instantiate(expression, locals, constructor);

      if (identifier) {
        addIdentifier(locals, identifier, instance, constructor || expression.name);
      }

      return instance;
    };

    function addIdentifier(locals, identifier, instance, name) {
      if (!(locals && isObject(locals.$scope))) {
        throw minErr('$controller')('noscp',
          'Cannot export controller \'{0}\' as \'{1}\'! No $scope object provided via `locals`.',
          name, identifier);
      }

      locals.$scope[identifier] = instance;
    }
  }];
}

/**
 * @ngdoc service
 * @name $document
 * @requires $window
 * @this
 *
 * @description
 * A {@link angular.element jQuery or jqLite} wrapper for the browser's `window.document` object.
 *
 * @example
   <example module="documentExample" name="document">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <p>$document title: <b ng-bind="title"></b></p>
         <p>window.document title: <b ng-bind="windowTitle"></b></p>
       </div>
     </file>
     <file name="script.js">
       angular.module('documentExample', [])
         .controller('ExampleController', ['$scope', '$document', function($scope, $document) {
           $scope.title = $document[0].title;
           $scope.windowTitle = angular.element(window.document)[0].title;
         }]);
     </file>
   </example>
 */
function $DocumentProvider() {
  this.$get = ['$window', function(window) {
    return jqLite(window.document);
  }];
}


/**
 * @private
 * @this
 * Listens for document visibility change and makes the current status accessible.
 */
function $$IsDocumentHiddenProvider() {
  this.$get = ['$document', '$rootScope', function($document, $rootScope) {
    var doc = $document[0];
    var hidden = doc && doc.hidden;

    $document.on('visibilitychange', changeListener);

    $rootScope.$on('$destroy', function() {
      $document.off('visibilitychange', changeListener);
    });

    function changeListener() {
      hidden = doc.hidden;
    }

    return function() {
      return hidden;
    };
  }];
}

/**
 * @ngdoc service
 * @name $exceptionHandler
 * @requires ng.$log
 * @this
 *
 * @description
 * Any uncaught exception in angular expressions is delegated to this service.
 * The default implementation simply delegates to `$log.error` which logs it into
 * the browser console.
 *
 * In unit tests, if `angular-mocks.js` is loaded, this service is overridden by
 * {@link ngMock.$exceptionHandler mock $exceptionHandler} which aids in testing.
 *
 * ## Example:
 *
 * The example below will overwrite the default `$exceptionHandler` in order to (a) log uncaught
 * errors to the backend for later inspection by the developers and (b) to use `$log.warn()` instead
 * of `$log.error()`.
 *
 * ```js
 *   angular.
 *     module('exceptionOverwrite', []).
 *     factory('$exceptionHandler', ['$log', 'logErrorsToBackend', function($log, logErrorsToBackend) {
 *       return function myExceptionHandler(exception, cause) {
 *         logErrorsToBackend(exception, cause);
 *         $log.warn(exception, cause);
 *       };
 *     }]);
 * ```
 *
 * <hr />
 * Note, that code executed in event-listeners (even those registered using jqLite's `on`/`bind`
 * methods) does not delegate exceptions to the {@link ng.$exceptionHandler $exceptionHandler}
 * (unless executed during a digest).
 *
 * If you wish, you can manually delegate exceptions, e.g.
 * `try { ... } catch(e) { $exceptionHandler(e); }`
 *
 * @param {Error} exception Exception associated with the error.
 * @param {string=} cause Optional information about the context in which
 *       the error was thrown.
 *
 */
function $ExceptionHandlerProvider() {
  this.$get = ['$log', function($log) {
    return function(exception, cause) {
      $log.error.apply($log, arguments);
    };
  }];
}

var $$ForceReflowProvider = /** @this */ function() {
  this.$get = ['$document', function($document) {
    return function(domNode) {
      //the line below will force the browser to perform a repaint so
      //that all the animated elements within the animation frame will
      //be properly updated and drawn on screen. This is required to
      //ensure that the preparation animation is properly flushed so that
      //the active state picks up from there. DO NOT REMOVE THIS LINE.
      //DO NOT OPTIMIZE THIS LINE. THE MINIFIER WILL REMOVE IT OTHERWISE WHICH
      //WILL RESULT IN AN UNPREDICTABLE BUG THAT IS VERY HARD TO TRACK DOWN AND
      //WILL TAKE YEARS AWAY FROM YOUR LIFE.
      if (domNode) {
        if (!domNode.nodeType && domNode instanceof jqLite) {
          domNode = domNode[0];
        }
      } else {
        domNode = $document[0].body;
      }
      return domNode.offsetWidth + 1;
    };
  }];
};

var APPLICATION_JSON = 'application/json';
var CONTENT_TYPE_APPLICATION_JSON = {'Content-Type': APPLICATION_JSON + ';charset=utf-8'};
var JSON_START = /^\[|^\{(?!\{)/;
var JSON_ENDS = {
  '[': /]$/,
  '{': /}$/
};
var JSON_PROTECTION_PREFIX = /^\)]\}',?\n/;
var $httpMinErr = minErr('$http');

function serializeValue(v) {
  if (isObject(v)) {
    return isDate(v) ? v.toISOString() : toJson(v);
  }
  return v;
}


/** @this */
function $HttpParamSerializerProvider() {
  /**
   * @ngdoc service
   * @name $httpParamSerializer
   * @description
   *
   * Default {@link $http `$http`} params serializer that converts objects to strings
   * according to the following rules:
   *
   * * `{'foo': 'bar'}` results in `foo=bar`
   * * `{'foo': Date.now()}` results in `foo=2015-04-01T09%3A50%3A49.262Z` (`toISOString()` and encoded representation of a Date object)
   * * `{'foo': ['bar', 'baz']}` results in `foo=bar&foo=baz` (repeated key for each array element)
   * * `{'foo': {'bar':'baz'}}` results in `foo=%7B%22bar%22%3A%22baz%22%7D` (stringified and encoded representation of an object)
   *
   * Note that serializer will sort the request parameters alphabetically.
   * */

  this.$get = function() {
    return function ngParamSerializer(params) {
      if (!params) return '';
      var parts = [];
      forEachSorted(params, function(value, key) {
        if (value === null || isUndefined(value)) return;
        if (isArray(value)) {
          forEach(value, function(v) {
            parts.push(encodeUriQuery(key)  + '=' + encodeUriQuery(serializeValue(v)));
          });
        } else {
          parts.push(encodeUriQuery(key) + '=' + encodeUriQuery(serializeValue(value)));
        }
      });

      return parts.join('&');
    };
  };
}

/** @this */
function $HttpParamSerializerJQLikeProvider() {
  /**
   * @ngdoc service
   * @name $httpParamSerializerJQLike
   *
   * @description
   *
   * Alternative {@link $http `$http`} params serializer that follows
   * jQuery's [`param()`](http://api.jquery.com/jquery.param/) method logic.
   * The serializer will also sort the params alphabetically.
   *
   * To use it for serializing `$http` request parameters, set it as the `paramSerializer` property:
   *
   * ```js
   * $http({
   *   url: myUrl,
   *   method: 'GET',
   *   params: myParams,
   *   paramSerializer: '$httpParamSerializerJQLike'
   * });
   * ```
   *
   * It is also possible to set it as the default `paramSerializer` in the
   * {@link $httpProvider#defaults `$httpProvider`}.
   *
   * Additionally, you can inject the serializer and use it explicitly, for example to serialize
   * form data for submission:
   *
   * ```js
   * .controller(function($http, $httpParamSerializerJQLike) {
   *   //...
   *
   *   $http({
   *     url: myUrl,
   *     method: 'POST',
   *     data: $httpParamSerializerJQLike(myData),
   *     headers: {
   *       'Content-Type': 'application/x-www-form-urlencoded'
   *     }
   *   });
   *
   * });
   * ```
   *
   * */
  this.$get = function() {
    return function jQueryLikeParamSerializer(params) {
      if (!params) return '';
      var parts = [];
      serialize(params, '', true);
      return parts.join('&');

      function serialize(toSerialize, prefix, topLevel) {
        if (toSerialize === null || isUndefined(toSerialize)) return;
        if (isArray(toSerialize)) {
          forEach(toSerialize, function(value, index) {
            serialize(value, prefix + '[' + (isObject(value) ? index : '') + ']');
          });
        } else if (isObject(toSerialize) && !isDate(toSerialize)) {
          forEachSorted(toSerialize, function(value, key) {
            serialize(value, prefix +
                (topLevel ? '' : '[') +
                key +
                (topLevel ? '' : ']'));
          });
        } else {
          parts.push(encodeUriQuery(prefix) + '=' + encodeUriQuery(serializeValue(toSerialize)));
        }
      }
    };
  };
}

function defaultHttpResponseTransform(data, headers) {
  if (isString(data)) {
    // Strip json vulnerability protection prefix and trim whitespace
    var tempData = data.replace(JSON_PROTECTION_PREFIX, '').trim();

    if (tempData) {
      var contentType = headers('Content-Type');
      if ((contentType && (contentType.indexOf(APPLICATION_JSON) === 0)) || isJsonLike(tempData)) {
        data = fromJson(tempData);
      }
    }
  }

  return data;
}

function isJsonLike(str) {
    var jsonStart = str.match(JSON_START);
    return jsonStart && JSON_ENDS[jsonStart[0]].test(str);
}

/**
 * Parse headers into key value object
 *
 * @param {string} headers Raw headers as a string
 * @returns {Object} Parsed headers as key value object
 */
function parseHeaders(headers) {
  var parsed = createMap(), i;

  function fillInParsed(key, val) {
    if (key) {
      parsed[key] = parsed[key] ? parsed[key] + ', ' + val : val;
    }
  }

  if (isString(headers)) {
    forEach(headers.split('\n'), function(line) {
      i = line.indexOf(':');
      fillInParsed(lowercase(trim(line.substr(0, i))), trim(line.substr(i + 1)));
    });
  } else if (isObject(headers)) {
    forEach(headers, function(headerVal, headerKey) {
      fillInParsed(lowercase(headerKey), trim(headerVal));
    });
  }

  return parsed;
}


/**
 * Returns a function that provides access to parsed headers.
 *
 * Headers are lazy parsed when first requested.
 * @see parseHeaders
 *
 * @param {(string|Object)} headers Headers to provide access to.
 * @returns {function(string=)} Returns a getter function which if called with:
 *
 *   - if called with an argument returns a single header value or null
 *   - if called with no arguments returns an object containing all headers.
 */
function headersGetter(headers) {
  var headersObj;

  return function(name) {
    if (!headersObj) headersObj =  parseHeaders(headers);

    if (name) {
      var value = headersObj[lowercase(name)];
      if (value === undefined) {
        value = null;
      }
      return value;
    }

    return headersObj;
  };
}


/**
 * Chain all given functions
 *
 * This function is used for both request and response transforming
 *
 * @param {*} data Data to transform.
 * @param {function(string=)} headers HTTP headers getter fn.
 * @param {number} status HTTP status code of the response.
 * @param {(Function|Array.<Function>)} fns Function or an array of functions.
 * @returns {*} Transformed data.
 */
function transformData(data, headers, status, fns) {
  if (isFunction(fns)) {
    return fns(data, headers, status);
  }

  forEach(fns, function(fn) {
    data = fn(data, headers, status);
  });

  return data;
}


function isSuccess(status) {
  return 200 <= status && status < 300;
}


/**
 * @ngdoc provider
 * @name $httpProvider
 * @this
 *
 * @description
 * Use `$httpProvider` to change the default behavior of the {@link ng.$http $http} service.
 * */
function $HttpProvider() {
  /**
   * @ngdoc property
   * @name $httpProvider#defaults
   * @description
   *
   * Object containing default values for all {@link ng.$http $http} requests.
   *
   * - **`defaults.cache`** - {boolean|Object} - A boolean value or object created with
   * {@link ng.$cacheFactory `$cacheFactory`} to enable or disable caching of HTTP responses
   * by default. See {@link $http#caching $http Caching} for more information.
   *
   * - **`defaults.xsrfCookieName`** - {string} - Name of cookie containing the XSRF token.
   * Defaults value is `'XSRF-TOKEN'`.
   *
   * - **`defaults.xsrfHeaderName`** - {string} - Name of HTTP header to populate with the
   * XSRF token. Defaults value is `'X-XSRF-TOKEN'`.
   *
   * - **`defaults.headers`** - {Object} - Default headers for all $http requests.
   * Refer to {@link ng.$http#setting-http-headers $http} for documentation on
   * setting default headers.
   *     - **`defaults.headers.common`**
   *     - **`defaults.headers.post`**
   *     - **`defaults.headers.put`**
   *     - **`defaults.headers.patch`**
   *
   *
   * - **`defaults.paramSerializer`** - `{string|function(Object<string,string>):string}` - A function
   *  used to the prepare string representation of request parameters (specified as an object).
   *  If specified as string, it is interpreted as a function registered with the {@link auto.$injector $injector}.
   *  Defaults to {@link ng.$httpParamSerializer $httpParamSerializer}.
   *
   * - **`defaults.jsonpCallbackParam`** - `{string}` - the name of the query parameter that passes the name of the
   * callback in a JSONP request. The value of this parameter will be replaced with the expression generated by the
   * {@link $jsonpCallbacks} service. Defaults to `'callback'`.
   *
   **/
  var defaults = this.defaults = {
    // transform incoming response data
    transformResponse: [defaultHttpResponseTransform],

    // transform outgoing request data
    transformRequest: [function(d) {
      return isObject(d) && !isFile(d) && !isBlob(d) && !isFormData(d) ? toJson(d) : d;
    }],

    // default headers
    headers: {
      common: {
        'Accept': 'application/json, text/plain, */*'
      },
      post:   shallowCopy(CONTENT_TYPE_APPLICATION_JSON),
      put:    shallowCopy(CONTENT_TYPE_APPLICATION_JSON),
      patch:  shallowCopy(CONTENT_TYPE_APPLICATION_JSON)
    },

    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',

    paramSerializer: '$httpParamSerializer',

    jsonpCallbackParam: 'callback'
  };

  var useApplyAsync = false;
  /**
   * @ngdoc method
   * @name $httpProvider#useApplyAsync
   * @description
   *
   * Configure $http service to combine processing of multiple http responses received at around
   * the same time via {@link ng.$rootScope.Scope#$applyAsync $rootScope.$applyAsync}. This can result in
   * significant performance improvement for bigger applications that make many HTTP requests
   * concurrently (common during application bootstrap).
   *
   * Defaults to false. If no value is specified, returns the current configured value.
   *
   * @param {boolean=} value If true, when requests are loaded, they will schedule a deferred
   *    "apply" on the next tick, giving time for subsequent requests in a roughly ~10ms window
   *    to load and share the same digest cycle.
   *
   * @returns {boolean|Object} If a value is specified, returns the $httpProvider for chaining.
   *    otherwise, returns the current configured value.
   **/
  this.useApplyAsync = function(value) {
    if (isDefined(value)) {
      useApplyAsync = !!value;
      return this;
    }
    return useApplyAsync;
  };

  /**
   * @ngdoc property
   * @name $httpProvider#interceptors
   * @description
   *
   * Array containing service factories for all synchronous or asynchronous {@link ng.$http $http}
   * pre-processing of request or postprocessing of responses.
   *
   * These service factories are ordered by request, i.e. they are applied in the same order as the
   * array, on request, but reverse order, on response.
   *
   * {@link ng.$http#interceptors Interceptors detailed info}
   **/
  var interceptorFactories = this.interceptors = [];

  this.$get = ['$browser', '$httpBackend', '$$cookieReader', '$cacheFactory', '$rootScope', '$q', '$injector', '$sce',
      function($browser, $httpBackend, $$cookieReader, $cacheFactory, $rootScope, $q, $injector, $sce) {

    var defaultCache = $cacheFactory('$http');

    /**
     * Make sure that default param serializer is exposed as a function
     */
    defaults.paramSerializer = isString(defaults.paramSerializer) ?
      $injector.get(defaults.paramSerializer) : defaults.paramSerializer;

    /**
     * Interceptors stored in reverse order. Inner interceptors before outer interceptors.
     * The reversal is needed so that we can build up the interception chain around the
     * server request.
     */
    var reversedInterceptors = [];

    forEach(interceptorFactories, function(interceptorFactory) {
      reversedInterceptors.unshift(isString(interceptorFactory)
          ? $injector.get(interceptorFactory) : $injector.invoke(interceptorFactory));
    });

    /**
     * @ngdoc service
     * @kind function
     * @name $http
     * @requires ng.$httpBackend
     * @requires $cacheFactory
     * @requires $rootScope
     * @requires $q
     * @requires $injector
     *
     * @description
     * The `$http` service is a core Angular service that facilitates communication with the remote
     * HTTP servers via the browser's [XMLHttpRequest](https://developer.mozilla.org/en/xmlhttprequest)
     * object or via [JSONP](http://en.wikipedia.org/wiki/JSONP).
     *
     * For unit testing applications that use `$http` service, see
     * {@link ngMock.$httpBackend $httpBackend mock}.
     *
     * For a higher level of abstraction, please check out the {@link ngResource.$resource
     * $resource} service.
     *
     * The $http API is based on the {@link ng.$q deferred/promise APIs} exposed by
     * the $q service. While for simple usage patterns this doesn't matter much, for advanced usage
     * it is important to familiarize yourself with these APIs and the guarantees they provide.
     *
     *
     * ## General usage
     * The `$http` service is a function which takes a single argument — a {@link $http#usage configuration object} —
     * that is used to generate an HTTP request and returns  a {@link ng.$q promise}.
     *
     * ```js
     *   // Simple GET request example:
     *   $http({
     *     method: 'GET',
     *     url: '/someUrl'
     *   }).then(function successCallback(response) {
     *       // this callback will be called asynchronously
     *       // when the response is available
     *     }, function errorCallback(response) {
     *       // called asynchronously if an error occurs
     *       // or server returns response with an error status.
     *     });
     * ```
     *
     * The response object has these properties:
     *
     *   - **data** – `{string|Object}` – The response body transformed with the transform
     *     functions.
     *   - **status** – `{number}` – HTTP status code of the response.
     *   - **headers** – `{function([headerName])}` – Header getter function.
     *   - **config** – `{Object}` – The configuration object that was used to generate the request.
     *   - **statusText** – `{string}` – HTTP status text of the response.
     *
     * A response status code between 200 and 299 is considered a success status and will result in
     * the success callback being called. Any response status code outside of that range is
     * considered an error status and will result in the error callback being called.
     * Also, status codes less than -1 are normalized to zero. -1 usually means the request was
     * aborted, e.g. using a `config.timeout`.
     * Note that if the response is a redirect, XMLHttpRequest will transparently follow it, meaning
     * that the outcome (success or error) will be determined by the final response status code.
     *
     *
     * ## Shortcut methods
     *
     * Shortcut methods are also available. All shortcut methods require passing in the URL, and
     * request data must be passed in for POST/PUT requests. An optional config can be passed as the
     * last argument.
     *
     * ```js
     *   $http.get('/someUrl', config).then(successCallback, errorCallback);
     *   $http.post('/someUrl', data, config).then(successCallback, errorCallback);
     * ```
     *
     * Complete list of shortcut methods:
     *
     * - {@link ng.$http#get $http.get}
     * - {@link ng.$http#head $http.head}
     * - {@link ng.$http#post $http.post}
     * - {@link ng.$http#put $http.put}
     * - {@link ng.$http#delete $http.delete}
     * - {@link ng.$http#jsonp $http.jsonp}
     * - {@link ng.$http#patch $http.patch}
     *
     *
     * ## Writing Unit Tests that use $http
     * When unit testing (using {@link ngMock ngMock}), it is necessary to call
     * {@link ngMock.$httpBackend#flush $httpBackend.flush()} to flush each pending
     * request using trained responses.
     *
     * ```
     * $httpBackend.expectGET(...);
     * $http.get(...);
     * $httpBackend.flush();
     * ```
     *
     * ## Setting HTTP Headers
     *
     * The $http service will automatically add certain HTTP headers to all requests. These defaults
     * can be fully configured by accessing the `$httpProvider.defaults.headers` configuration
     * object, which currently contains this default configuration:
     *
     * - `$httpProvider.defaults.headers.common` (headers that are common for all requests):
     *   - <code>Accept: application/json, text/plain, \*&#65279;/&#65279;\*</code>
     * - `$httpProvider.defaults.headers.post`: (header defaults for POST requests)
     *   - `Content-Type: application/json`
     * - `$httpProvider.defaults.headers.put` (header defaults for PUT requests)
     *   - `Content-Type: application/json`
     *
     * To add or overwrite these defaults, simply add or remove a property from these configuration
     * objects. To add headers for an HTTP method other than POST or PUT, simply add a new object
     * with the lowercased HTTP method name as the key, e.g.
     * `$httpProvider.defaults.headers.get = { 'My-Header' : 'value' }`.
     *
     * The defaults can also be set at runtime via the `$http.defaults` object in the same
     * fashion. For example:
     *
     * ```
     * module.run(function($http) {
     *   $http.defaults.headers.common.Authorization = 'Basic YmVlcDpib29w';
     * });
     * ```
     *
     * In addition, you can supply a `headers` property in the config object passed when
     * calling `$http(config)`, which overrides the defaults without changing them globally.
     *
     * To explicitly remove a header automatically added via $httpProvider.defaults.headers on a per request basis,
     * Use the `headers` property, setting the desired header to `undefined`. For example:
     *
     * ```js
     * var req = {
     *  method: 'POST',
     *  url: 'http://example.com',
     *  headers: {
     *    'Content-Type': undefined
     *  },
     *  data: { test: 'test' }
     * }
     *
     * $http(req).then(function(){...}, function(){...});
     * ```
     *
     * ## Transforming Requests and Responses
     *
     * Both requests and responses can be transformed using transformation functions: `transformRequest`
     * and `transformResponse`. These properties can be a single function that returns
     * the transformed value (`function(data, headersGetter, status)`) or an array of such transformation functions,
     * which allows you to `push` or `unshift` a new transformation function into the transformation chain.
     *
     * <div class="alert alert-warning">
     * **Note:** Angular does not make a copy of the `data` parameter before it is passed into the `transformRequest` pipeline.
     * That means changes to the properties of `data` are not local to the transform function (since Javascript passes objects by reference).
     * For example, when calling `$http.get(url, $scope.myObject)`, modifications to the object's properties in a transformRequest
     * function will be reflected on the scope and in any templates where the object is data-bound.
     * To prevent this, transform functions should have no side-effects.
     * If you need to modify properties, it is recommended to make a copy of the data, or create new object to return.
     * </div>
     *
     * ### Default Transformations
     *
     * The `$httpProvider` provider and `$http` service expose `defaults.transformRequest` and
     * `defaults.transformResponse` properties. If a request does not provide its own transformations
     * then these will be applied.
     *
     * You can augment or replace the default transformations by modifying these properties by adding to or
     * replacing the array.
     *
     * Angular provides the following default transformations:
     *
     * Request transformations (`$httpProvider.defaults.transformRequest` and `$http.defaults.transformRequest`):
     *
     * - If the `data` property of the request configuration object contains an object, serialize it
     *   into JSON format.
     *
     * Response transformations (`$httpProvider.defaults.transformResponse` and `$http.defaults.transformResponse`):
     *
     *  - If XSRF prefix is detected, strip it (see Security Considerations section below).
     *  - If JSON response is detected, deserialize it using a JSON parser.
     *
     *
     * ### Overriding the Default Transformations Per Request
     *
     * If you wish to override the request/response transformations only for a single request then provide
     * `transformRequest` and/or `transformResponse` properties on the configuration object passed
     * into `$http`.
     *
     * Note that if you provide these properties on the config object the default transformations will be
     * overwritten. If you wish to augment the default transformations then you must include them in your
     * local transformation array.
     *
     * The following code demonstrates adding a new response transformation to be run after the default response
     * transformations have been run.
     *
     * ```js
     * function appendTransform(defaults, transform) {
     *
     *   // We can't guarantee that the default transformation is an array
     *   defaults = angular.isArray(defaults) ? defaults : [defaults];
     *
     *   // Append the new transformation to the defaults
     *   return defaults.concat(transform);
     * }
     *
     * $http({
     *   url: '...',
     *   method: 'GET',
     *   transformResponse: appendTransform($http.defaults.transformResponse, function(value) {
     *     return doTransform(value);
     *   })
     * });
     * ```
     *
     *
     * ## Caching
     *
     * {@link ng.$http `$http`} responses are not cached by default. To enable caching, you must
     * set the config.cache value or the default cache value to TRUE or to a cache object (created
     * with {@link ng.$cacheFactory `$cacheFactory`}). If defined, the value of config.cache takes
     * precedence over the default cache value.
     *
     * In order to:
     *   * cache all responses - set the default cache value to TRUE or to a cache object
     *   * cache a specific response - set config.cache value to TRUE or to a cache object
     *
     * If caching is enabled, but neither the default cache nor config.cache are set to a cache object,
     * then the default `$cacheFactory("$http")` object is used.
     *
     * The default cache value can be set by updating the
     * {@link ng.$http#defaults `$http.defaults.cache`} property or the
     * {@link $httpProvider#defaults `$httpProvider.defaults.cache`} property.
     *
     * When caching is enabled, {@link ng.$http `$http`} stores the response from the server using
     * the relevant cache object. The next time the same request is made, the response is returned
     * from the cache without sending a request to the server.
     *
     * Take note that:
     *
     *   * Only GET and JSONP requests are cached.
     *   * The cache key is the request URL including search parameters; headers are not considered.
     *   * Cached responses are returned asynchronously, in the same way as responses from the server.
     *   * If multiple identical requests are made using the same cache, which is not yet populated,
     *     one request will be made to the server and remaining requests will return the same response.
     *   * A cache-control header on the response does not affect if or how responses are cached.
     *
     *
     * ## Interceptors
     *
     * Before you start creating interceptors, be sure to understand the
     * {@link ng.$q $q and deferred/promise APIs}.
     *
     * For purposes of global error handling, authentication, or any kind of synchronous or
     * asynchronous pre-processing of request or postprocessing of responses, it is desirable to be
     * able to intercept requests before they are handed to the server and
     * responses before they are handed over to the application code that
     * initiated these requests. The interceptors leverage the {@link ng.$q
     * promise APIs} to fulfill this need for both synchronous and asynchronous pre-processing.
     *
     * The interceptors are service factories that are registered with the `$httpProvider` by
     * adding them to the `$httpProvider.interceptors` array. The factory is called and
     * injected with dependencies (if specified) and returns the interceptor.
     *
     * There are two kinds of interceptors (and two kinds of rejection interceptors):
     *
     *   * `request`: interceptors get called with a http {@link $http#usage config} object. The function is free to
     *     modify the `config` object or create a new one. The function needs to return the `config`
     *     object directly, or a promise containing the `config` or a new `config` object.
     *   * `requestError`: interceptor gets called when a previous interceptor threw an error or
     *     resolved with a rejection.
     *   * `response`: interceptors get called with http `response` object. The function is free to
     *     modify the `response` object or create a new one. The function needs to return the `response`
     *     object directly, or as a promise containing the `response` or a new `response` object.
     *   * `responseError`: interceptor gets called when a previous interceptor threw an error or
     *     resolved with a rejection.
     *
     *
     * ```js
     *   // register the interceptor as a service
     *   $provide.factory('myHttpInterceptor', function($q, dependency1, dependency2) {
     *     return {
     *       // optional method
     *       'request': function(config) {
     *         // do something on success
     *         return config;
     *       },
     *
     *       // optional method
     *      'requestError': function(rejection) {
     *         // do something on error
     *         if (canRecover(rejection)) {
     *           return responseOrNewPromise
     *         }
     *         return $q.reject(rejection);
     *       },
     *
     *
     *
     *       // optional method
     *       'response': function(response) {
     *         // do something on success
     *         return response;
     *       },
     *
     *       // optional method
     *      'responseError': function(rejection) {
     *         // do something on error
     *         if (canRecover(rejection)) {
     *           return responseOrNewPromise
     *         }
     *         return $q.reject(rejection);
     *       }
     *     };
     *   });
     *
     *   $httpProvider.interceptors.push('myHttpInterceptor');
     *
     *
     *   // alternatively, register the interceptor via an anonymous factory
     *   $httpProvider.interceptors.push(function($q, dependency1, dependency2) {
     *     return {
     *      'request': function(config) {
     *          // same as above
     *       },
     *
     *       'response': function(response) {
     *          // same as above
     *       }
     *     };
     *   });
     * ```
     *
     * ## Security Considerations
     *
     * When designing web applications, consider security threats from:
     *
     * - [JSON vulnerability](http://haacked.com/archive/2008/11/20/anatomy-of-a-subtle-json-vulnerability.aspx)
     * - [XSRF](http://en.wikipedia.org/wiki/Cross-site_request_forgery)
     *
     * Both server and the client must cooperate in order to eliminate these threats. Angular comes
     * pre-configured with strategies that address these issues, but for this to work backend server
     * cooperation is required.
     *
     * ### JSON Vulnerability Protection
     *
     * A [JSON vulnerability](http://haacked.com/archive/2008/11/20/anatomy-of-a-subtle-json-vulnerability.aspx)
     * allows third party website to turn your JSON resource URL into
     * [JSONP](http://en.wikipedia.org/wiki/JSONP) request under some conditions. To
     * counter this your server can prefix all JSON requests with following string `")]}',\n"`.
     * Angular will automatically strip the prefix before processing it as JSON.
     *
     * For example if your server needs to return:
     * ```js
     * ['one','two']
     * ```
     *
     * which is vulnerable to attack, your server can return:
     * ```js
     * )]}',
     * ['one','two']
     * ```
     *
     * Angular will strip the prefix, before processing the JSON.
     *
     *
     * ### Cross Site Request Forgery (XSRF) Protection
     *
     * [XSRF](http://en.wikipedia.org/wiki/Cross-site_request_forgery) is an attack technique by
     * which the attacker can trick an authenticated user into unknowingly executing actions on your
     * website. Angular provides a mechanism to counter XSRF. When performing XHR requests, the
     * $http service reads a token from a cookie (by default, `XSRF-TOKEN`) and sets it as an HTTP
     * header (`X-XSRF-TOKEN`). Since only JavaScript that runs on your domain could read the
     * cookie, your server can be assured that the XHR came from JavaScript running on your domain.
     * The header will not be set for cross-domain requests.
     *
     * To take advantage of this, your server needs to set a token in a JavaScript readable session
     * cookie called `XSRF-TOKEN` on the first HTTP GET request. On subsequent XHR requests the
     * server can verify that the cookie matches `X-XSRF-TOKEN` HTTP header, and therefore be sure
     * that only JavaScript running on your domain could have sent the request. The token must be
     * unique for each user and must be verifiable by the server (to prevent the JavaScript from
     * making up its own tokens). We recommend that the token is a digest of your site's
     * authentication cookie with a [salt](https://en.wikipedia.org/wiki/Salt_(cryptography&#41;)
     * for added security.
     *
     * The name of the headers can be specified using the xsrfHeaderName and xsrfCookieName
     * properties of either $httpProvider.defaults at config-time, $http.defaults at run-time,
     * or the per-request config object.
     *
     * In order to prevent collisions in environments where multiple Angular apps share the
     * same domain or subdomain, we recommend that each application uses unique cookie name.
     *
     * @param {object} config Object describing the request to be made and how it should be
     *    processed. The object has following properties:
     *
     *    - **method** – `{string}` – HTTP method (e.g. 'GET', 'POST', etc)
     *    - **url** – `{string|TrustedObject}` – Absolute or relative URL of the resource that is being requested;
     *      or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     *    - **params** – `{Object.<string|Object>}` – Map of strings or objects which will be serialized
     *      with the `paramSerializer` and appended as GET parameters.
     *    - **data** – `{string|Object}` – Data to be sent as the request message data.
     *    - **headers** – `{Object}` – Map of strings or functions which return strings representing
     *      HTTP headers to send to the server. If the return value of a function is null, the
     *      header will not be sent. Functions accept a config object as an argument.
     *    - **eventHandlers** - `{Object}` - Event listeners to be bound to the XMLHttpRequest object.
     *      To bind events to the XMLHttpRequest upload object, use `uploadEventHandlers`.
     *      The handler will be called in the context of a `$apply` block.
     *    - **uploadEventHandlers** - `{Object}` - Event listeners to be bound to the XMLHttpRequest upload
     *      object. To bind events to the XMLHttpRequest object, use `eventHandlers`.
     *      The handler will be called in the context of a `$apply` block.
     *    - **xsrfHeaderName** – `{string}` – Name of HTTP header to populate with the XSRF token.
     *    - **xsrfCookieName** – `{string}` – Name of cookie containing the XSRF token.
     *    - **transformRequest** –
     *      `{function(data, headersGetter)|Array.<function(data, headersGetter)>}` –
     *      transform function or an array of such functions. The transform function takes the http
     *      request body and headers and returns its transformed (typically serialized) version.
     *      See {@link ng.$http#overriding-the-default-transformations-per-request
     *      Overriding the Default Transformations}
     *    - **transformResponse** –
     *      `{function(data, headersGetter, status)|Array.<function(data, headersGetter, status)>}` –
     *      transform function or an array of such functions. The transform function takes the http
     *      response body, headers and status and returns its transformed (typically deserialized) version.
     *      See {@link ng.$http#overriding-the-default-transformations-per-request
     *      Overriding the Default Transformations}
     *    - **paramSerializer** - `{string|function(Object<string,string>):string}` - A function used to
     *      prepare the string representation of request parameters (specified as an object).
     *      If specified as string, it is interpreted as function registered with the
     *      {@link $injector $injector}, which means you can create your own serializer
     *      by registering it as a {@link auto.$provide#service service}.
     *      The default serializer is the {@link $httpParamSerializer $httpParamSerializer};
     *      alternatively, you can use the {@link $httpParamSerializerJQLike $httpParamSerializerJQLike}
     *    - **cache** – `{boolean|Object}` – A boolean value or object created with
     *      {@link ng.$cacheFactory `$cacheFactory`} to enable or disable caching of the HTTP response.
     *      See {@link $http#caching $http Caching} for more information.
     *    - **timeout** – `{number|Promise}` – timeout in milliseconds, or {@link ng.$q promise}
     *      that should abort the request when resolved.
     *    - **withCredentials** - `{boolean}` - whether to set the `withCredentials` flag on the
     *      XHR object. See [requests with credentials](https://developer.mozilla.org/docs/Web/HTTP/Access_control_CORS#Requests_with_credentials)
     *      for more information.
     *    - **responseType** - `{string}` - see
     *      [XMLHttpRequest.responseType](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest#xmlhttprequest-responsetype).
     *
     * @returns {HttpPromise} Returns a {@link ng.$q `Promise}` that will be resolved to a response object
     *                        when the request succeeds or fails.
     *
     *
     * @property {Array.<Object>} pendingRequests Array of config objects for currently pending
     *   requests. This is primarily meant to be used for debugging purposes.
     *
     *
     * @example
<example module="httpExample" name="http-service">
<file name="index.html">
  <div ng-controller="FetchController">
    <select ng-model="method" aria-label="Request method">
      <option>GET</option>
      <option>JSONP</option>
    </select>
    <input type="text" ng-model="url" size="80" aria-label="URL" />
    <button id="fetchbtn" ng-click="fetch()">fetch</button><br>
    <button id="samplegetbtn" ng-click="updateModel('GET', 'http-hello.html')">Sample GET</button>
    <button id="samplejsonpbtn"
      ng-click="updateModel('JSONP',
                    'https://angularjs.org/greet.php?name=Super%20Hero')">
      Sample JSONP
    </button>
    <button id="invalidjsonpbtn"
      ng-click="updateModel('JSONP', 'https://angularjs.org/doesntexist')">
        Invalid JSONP
      </button>
    <pre>http status code: {{status}}</pre>
    <pre>http response data: {{data}}</pre>
  </div>
</file>
<file name="script.js">
  angular.module('httpExample', [])
    .config(['$sceDelegateProvider', function($sceDelegateProvider) {
      // We must whitelist the JSONP endpoint that we are using to show that we trust it
      $sceDelegateProvider.resourceUrlWhitelist([
        'self',
        'https://angularjs.org/**'
      ]);
    }])
    .controller('FetchController', ['$scope', '$http', '$templateCache',
      function($scope, $http, $templateCache) {
        $scope.method = 'GET';
        $scope.url = 'http-hello.html';

        $scope.fetch = function() {
          $scope.code = null;
          $scope.response = null;

          $http({method: $scope.method, url: $scope.url, cache: $templateCache}).
            then(function(response) {
              $scope.status = response.status;
              $scope.data = response.data;
            }, function(response) {
              $scope.data = response.data || 'Request failed';
              $scope.status = response.status;
          });
        };

        $scope.updateModel = function(method, url) {
          $scope.method = method;
          $scope.url = url;
        };
      }]);
</file>
<file name="http-hello.html">
  Hello, $http!
</file>
<file name="protractor.js" type="protractor">
  var status = element(by.binding('status'));
  var data = element(by.binding('data'));
  var fetchBtn = element(by.id('fetchbtn'));
  var sampleGetBtn = element(by.id('samplegetbtn'));
  var invalidJsonpBtn = element(by.id('invalidjsonpbtn'));

  it('should make an xhr GET request', function() {
    sampleGetBtn.click();
    fetchBtn.click();
    expect(status.getText()).toMatch('200');
    expect(data.getText()).toMatch(/Hello, \$http!/);
  });

// Commented out due to flakes. See https://github.com/angular/angular.js/issues/9185
// it('should make a JSONP request to angularjs.org', function() {
//   var sampleJsonpBtn = element(by.id('samplejsonpbtn'));
//   sampleJsonpBtn.click();
//   fetchBtn.click();
//   expect(status.getText()).toMatch('200');
//   expect(data.getText()).toMatch(/Super Hero!/);
// });

  it('should make JSONP request to invalid URL and invoke the error handler',
      function() {
    invalidJsonpBtn.click();
    fetchBtn.click();
    expect(status.getText()).toMatch('0');
    expect(data.getText()).toMatch('Request failed');
  });
</file>
</example>
     */
    function $http(requestConfig) {

      if (!isObject(requestConfig)) {
        throw minErr('$http')('badreq', 'Http request configuration must be an object.  Received: {0}', requestConfig);
      }

      if (!isString($sce.valueOf(requestConfig.url))) {
        throw minErr('$http')('badreq', 'Http request configuration url must be a string or a $sce trusted object.  Received: {0}', requestConfig.url);
      }

      var config = extend({
        method: 'get',
        transformRequest: defaults.transformRequest,
        transformResponse: defaults.transformResponse,
        paramSerializer: defaults.paramSerializer,
        jsonpCallbackParam: defaults.jsonpCallbackParam
      }, requestConfig);

      config.headers = mergeHeaders(requestConfig);
      config.method = uppercase(config.method);
      config.paramSerializer = isString(config.paramSerializer) ?
          $injector.get(config.paramSerializer) : config.paramSerializer;

      $browser.$$incOutstandingRequestCount();

      var requestInterceptors = [];
      var responseInterceptors = [];
      var promise = $q.resolve(config);

      // apply interceptors
      forEach(reversedInterceptors, function(interceptor) {
        if (interceptor.request || interceptor.requestError) {
          requestInterceptors.unshift(interceptor.request, interceptor.requestError);
        }
        if (interceptor.response || interceptor.responseError) {
          responseInterceptors.push(interceptor.response, interceptor.responseError);
        }
      });

      promise = chainInterceptors(promise, requestInterceptors);
      promise = promise.then(serverRequest);
      promise = chainInterceptors(promise, responseInterceptors);
      promise = promise.finally(completeOutstandingRequest);

      return promise;


      function chainInterceptors(promise, interceptors) {
        for (var i = 0, ii = interceptors.length; i < ii;) {
          var thenFn = interceptors[i++];
          var rejectFn = interceptors[i++];

          promise = promise.then(thenFn, rejectFn);
        }

        interceptors.length = 0;

        return promise;
      }

      function completeOutstandingRequest() {
        $browser.$$completeOutstandingRequest(noop);
      }

      function executeHeaderFns(headers, config) {
        var headerContent, processedHeaders = {};

        forEach(headers, function(headerFn, header) {
          if (isFunction(headerFn)) {
            headerContent = headerFn(config);
            if (headerContent != null) {
              processedHeaders[header] = headerContent;
            }
          } else {
            processedHeaders[header] = headerFn;
          }
        });

        return processedHeaders;
      }

      function mergeHeaders(config) {
        var defHeaders = defaults.headers,
            reqHeaders = extend({}, config.headers),
            defHeaderName, lowercaseDefHeaderName, reqHeaderName;

        defHeaders = extend({}, defHeaders.common, defHeaders[lowercase(config.method)]);

        // using for-in instead of forEach to avoid unnecessary iteration after header has been found
        defaultHeadersIteration:
        for (defHeaderName in defHeaders) {
          lowercaseDefHeaderName = lowercase(defHeaderName);

          for (reqHeaderName in reqHeaders) {
            if (lowercase(reqHeaderName) === lowercaseDefHeaderName) {
              continue defaultHeadersIteration;
            }
          }

          reqHeaders[defHeaderName] = defHeaders[defHeaderName];
        }

        // execute if header value is a function for merged headers
        return executeHeaderFns(reqHeaders, shallowCopy(config));
      }

      function serverRequest(config) {
        var headers = config.headers;
        var reqData = transformData(config.data, headersGetter(headers), undefined, config.transformRequest);

        // strip content-type if data is undefined
        if (isUndefined(reqData)) {
          forEach(headers, function(value, header) {
            if (lowercase(header) === 'content-type') {
              delete headers[header];
            }
          });
        }

        if (isUndefined(config.withCredentials) && !isUndefined(defaults.withCredentials)) {
          config.withCredentials = defaults.withCredentials;
        }

        // send request
        return sendReq(config, reqData).then(transformResponse, transformResponse);
      }

      function transformResponse(response) {
        // make a copy since the response must be cacheable
        var resp = extend({}, response);
        resp.data = transformData(response.data, response.headers, response.status,
                                  config.transformResponse);
        return (isSuccess(response.status))
          ? resp
          : $q.reject(resp);
      }
    }

    $http.pendingRequests = [];

    /**
     * @ngdoc method
     * @name $http#get
     *
     * @description
     * Shortcut method to perform `GET` request.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */

    /**
     * @ngdoc method
     * @name $http#delete
     *
     * @description
     * Shortcut method to perform `DELETE` request.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */

    /**
     * @ngdoc method
     * @name $http#head
     *
     * @description
     * Shortcut method to perform `HEAD` request.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */

    /**
     * @ngdoc method
     * @name $http#jsonp
     *
     * @description
     * Shortcut method to perform `JSONP` request.
     *
     * Note that, since JSONP requests are sensitive because the response is given full access to the browser,
     * the url must be declared, via {@link $sce} as a trusted resource URL.
     * You can trust a URL by adding it to the whitelist via
     * {@link $sceDelegateProvider#resourceUrlWhitelist  `$sceDelegateProvider.resourceUrlWhitelist`} or
     * by explicitly trusting the URL via {@link $sce#trustAsResourceUrl `$sce.trustAsResourceUrl(url)`}.
     *
     * JSONP requests must specify a callback to be used in the response from the server. This callback
     * is passed as a query parameter in the request. You must specify the name of this parameter by
     * setting the `jsonpCallbackParam` property on the request config object.
     *
     * ```
     * $http.jsonp('some/trusted/url', {jsonpCallbackParam: 'callback'})
     * ```
     *
     * You can also specify a default callback parameter name in `$http.defaults.jsonpCallbackParam`.
     * Initially this is set to `'callback'`.
     *
     * <div class="alert alert-danger">
     * You can no longer use the `JSON_CALLBACK` string as a placeholder for specifying where the callback
     * parameter value should go.
     * </div>
     *
     * If you would like to customise where and how the callbacks are stored then try overriding
     * or decorating the {@link $jsonpCallbacks} service.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */
    createShortMethods('get', 'delete', 'head', 'jsonp');

    /**
     * @ngdoc method
     * @name $http#post
     *
     * @description
     * Shortcut method to perform `POST` request.
     *
     * @param {string} url Relative or absolute URL specifying the destination of the request
     * @param {*} data Request content
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */

    /**
     * @ngdoc method
     * @name $http#put
     *
     * @description
     * Shortcut method to perform `PUT` request.
     *
     * @param {string} url Relative or absolute URL specifying the destination of the request
     * @param {*} data Request content
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */

     /**
      * @ngdoc method
      * @name $http#patch
      *
      * @description
      * Shortcut method to perform `PATCH` request.
      *
      * @param {string} url Relative or absolute URL specifying the destination of the request
      * @param {*} data Request content
      * @param {Object=} config Optional configuration object
      * @returns {HttpPromise} Future object
      */
    createShortMethodsWithData('post', 'put', 'patch');

        /**
         * @ngdoc property
         * @name $http#defaults
         *
         * @description
         * Runtime equivalent of the `$httpProvider.defaults` property. Allows configuration of
         * default headers, withCredentials as well as request and response transformations.
         *
         * See "Setting HTTP Headers" and "Transforming Requests and Responses" sections above.
         */
    $http.defaults = defaults;


    return $http;


    function createShortMethods(names) {
      forEach(arguments, function(name) {
        $http[name] = function(url, config) {
          return $http(extend({}, config || {}, {
            method: name,
            url: url
          }));
        };
      });
    }


    function createShortMethodsWithData(name) {
      forEach(arguments, function(name) {
        $http[name] = function(url, data, config) {
          return $http(extend({}, config || {}, {
            method: name,
            url: url,
            data: data
          }));
        };
      });
    }


    /**
     * Makes the request.
     *
     * !!! ACCESSES CLOSURE VARS:
     * $httpBackend, defaults, $log, $rootScope, defaultCache, $http.pendingRequests
     */
    function sendReq(config, reqData) {
      var deferred = $q.defer(),
          promise = deferred.promise,
          cache,
          cachedResp,
          reqHeaders = config.headers,
          isJsonp = lowercase(config.method) === 'jsonp',
          url = config.url;

      if (isJsonp) {
        // JSONP is a pretty sensitive operation where we're allowing a script to have full access to
        // our DOM and JS space.  So we require that the URL satisfies SCE.RESOURCE_URL.
        url = $sce.getTrustedResourceUrl(url);
      } else if (!isString(url)) {
        // If it is not a string then the URL must be a $sce trusted object
        url = $sce.valueOf(url);
      }

      url = buildUrl(url, config.paramSerializer(config.params));

      if (isJsonp) {
        // Check the url and add the JSONP callback placeholder
        url = sanitizeJsonpCallbackParam(url, config.jsonpCallbackParam);
      }

      $http.pendingRequests.push(config);
      promise.then(removePendingReq, removePendingReq);

      if ((config.cache || defaults.cache) && config.cache !== false &&
          (config.method === 'GET' || config.method === 'JSONP')) {
        cache = isObject(config.cache) ? config.cache
            : isObject(/** @type {?} */ (defaults).cache)
              ? /** @type {?} */ (defaults).cache
              : defaultCache;
      }

      if (cache) {
        cachedResp = cache.get(url);
        if (isDefined(cachedResp)) {
          if (isPromiseLike(cachedResp)) {
            // cached request has already been sent, but there is no response yet
            cachedResp.then(resolvePromiseWithResult, resolvePromiseWithResult);
          } else {
            // serving from cache
            if (isArray(cachedResp)) {
              resolvePromise(cachedResp[1], cachedResp[0], shallowCopy(cachedResp[2]), cachedResp[3]);
            } else {
              resolvePromise(cachedResp, 200, {}, 'OK');
            }
          }
        } else {
          // put the promise for the non-transformed response into cache as a placeholder
          cache.put(url, promise);
        }
      }


      // if we won't have the response in cache, set the xsrf headers and
      // send the request to the backend
      if (isUndefined(cachedResp)) {
        var xsrfValue = urlIsSameOrigin(config.url)
            ? $$cookieReader()[config.xsrfCookieName || defaults.xsrfCookieName]
            : undefined;
        if (xsrfValue) {
          reqHeaders[(config.xsrfHeaderName || defaults.xsrfHeaderName)] = xsrfValue;
        }

        $httpBackend(config.method, url, reqData, done, reqHeaders, config.timeout,
            config.withCredentials, config.responseType,
            createApplyHandlers(config.eventHandlers),
            createApplyHandlers(config.uploadEventHandlers));
      }

      return promise;

      function createApplyHandlers(eventHandlers) {
        if (eventHandlers) {
          var applyHandlers = {};
          forEach(eventHandlers, function(eventHandler, key) {
            applyHandlers[key] = function(event) {
              if (useApplyAsync) {
                $rootScope.$applyAsync(callEventHandler);
              } else if ($rootScope.$$phase) {
                callEventHandler();
              } else {
                $rootScope.$apply(callEventHandler);
              }

              function callEventHandler() {
                eventHandler(event);
              }
            };
          });
          return applyHandlers;
        }
      }


      /**
       * Callback registered to $httpBackend():
       *  - caches the response if desired
       *  - resolves the raw $http promise
       *  - calls $apply
       */
      function done(status, response, headersString, statusText) {
        if (cache) {
          if (isSuccess(status)) {
            cache.put(url, [status, response, parseHeaders(headersString), statusText]);
          } else {
            // remove promise from the cache
            cache.remove(url);
          }
        }

        function resolveHttpPromise() {
          resolvePromise(response, status, headersString, statusText);
        }

        if (useApplyAsync) {
          $rootScope.$applyAsync(resolveHttpPromise);
        } else {
          resolveHttpPromise();
          if (!$rootScope.$$phase) $rootScope.$apply();
        }
      }


      /**
       * Resolves the raw $http promise.
       */
      function resolvePromise(response, status, headers, statusText) {
        //status: HTTP response status code, 0, -1 (aborted by timeout / promise)
        status = status >= -1 ? status : 0;

        (isSuccess(status) ? deferred.resolve : deferred.reject)({
          data: response,
          status: status,
          headers: headersGetter(headers),
          config: config,
          statusText: statusText
        });
      }

      function resolvePromiseWithResult(result) {
        resolvePromise(result.data, result.status, shallowCopy(result.headers()), result.statusText);
      }

      function removePendingReq() {
        var idx = $http.pendingRequests.indexOf(config);
        if (idx !== -1) $http.pendingRequests.splice(idx, 1);
      }
    }


    function buildUrl(url, serializedParams) {
      if (serializedParams.length > 0) {
        url += ((url.indexOf('?') === -1) ? '?' : '&') + serializedParams;
      }
      return url;
    }

    function sanitizeJsonpCallbackParam(url, key) {
      if (/[&?][^=]+=JSON_CALLBACK/.test(url)) {
        // Throw if the url already contains a reference to JSON_CALLBACK
        throw $httpMinErr('badjsonp', 'Illegal use of JSON_CALLBACK in url, "{0}"', url);
      }

      var callbackParamRegex = new RegExp('[&?]' + key + '=');
      if (callbackParamRegex.test(url)) {
        // Throw if the callback param was already provided
        throw $httpMinErr('badjsonp', 'Illegal use of callback param, "{0}", in url, "{1}"', key, url);
      }

      // Add in the JSON_CALLBACK callback param value
      url += ((url.indexOf('?') === -1) ? '?' : '&') + key + '=JSON_CALLBACK';

      return url;
    }
  }];
}

/**
 * @ngdoc service
 * @name $xhrFactory
 * @this
 *
 * @description
 * Factory function used to create XMLHttpRequest objects.
 *
 * Replace or decorate this service to create your own custom XMLHttpRequest objects.
 *
 * ```
 * angular.module('myApp', [])
 * .factory('$xhrFactory', function() {
 *   return function createXhr(method, url) {
 *     return new window.XMLHttpRequest({mozSystem: true});
 *   };
 * });
 * ```
 *
 * @param {string} method HTTP method of the request (GET, POST, PUT, ..)
 * @param {string} url URL of the request.
 */
function $xhrFactoryProvider() {
  this.$get = function() {
    return function createXhr() {
      return new window.XMLHttpRequest();
    };
  };
}

/**
 * @ngdoc service
 * @name $httpBackend
 * @requires $jsonpCallbacks
 * @requires $document
 * @requires $xhrFactory
 * @this
 *
 * @description
 * HTTP backend used by the {@link ng.$http service} that delegates to
 * XMLHttpRequest object or JSONP and deals with browser incompatibilities.
 *
 * You should never need to use this service directly, instead use the higher-level abstractions:
 * {@link ng.$http $http} or {@link ngResource.$resource $resource}.
 *
 * During testing this implementation is swapped with {@link ngMock.$httpBackend mock
 * $httpBackend} which can be trained with responses.
 */
function $HttpBackendProvider() {
  this.$get = ['$browser', '$jsonpCallbacks', '$document', '$xhrFactory', function($browser, $jsonpCallbacks, $document, $xhrFactory) {
    return createHttpBackend($browser, $xhrFactory, $browser.defer, $jsonpCallbacks, $document[0]);
  }];
}

function createHttpBackend($browser, createXhr, $browserDefer, callbacks, rawDocument) {
  // TODO(vojta): fix the signature
  return function(method, url, post, callback, headers, timeout, withCredentials, responseType, eventHandlers, uploadEventHandlers) {
    url = url || $browser.url();

    if (lowercase(method) === 'jsonp') {
      var callbackPath = callbacks.createCallback(url);
      var jsonpDone = jsonpReq(url, callbackPath, function(status, text) {
        // jsonpReq only ever sets status to 200 (OK), 404 (ERROR) or -1 (WAITING)
        var response = (status === 200) && callbacks.getResponse(callbackPath);
        completeRequest(callback, status, response, '', text);
        callbacks.removeCallback(callbackPath);
      });
    } else {

      var xhr = createXhr(method, url);

      xhr.open(method, url, true);
      forEach(headers, function(value, key) {
        if (isDefined(value)) {
            xhr.setRequestHeader(key, value);
        }
      });

      xhr.onload = function requestLoaded() {
        var statusText = xhr.statusText || '';

        // responseText is the old-school way of retrieving response (supported by IE9)
        // response/responseType properties were introduced in XHR Level2 spec (supported by IE10)
        var response = ('response' in xhr) ? xhr.response : xhr.responseText;

        // normalize IE9 bug (http://bugs.jquery.com/ticket/1450)
        var status = xhr.status === 1223 ? 204 : xhr.status;

        // fix status code when it is 0 (0 status is undocumented).
        // Occurs when accessing file resources or on Android 4.1 stock browser
        // while retrieving files from application cache.
        if (status === 0) {
          status = response ? 200 : urlResolve(url).protocol === 'file' ? 404 : 0;
        }

        completeRequest(callback,
            status,
            response,
            xhr.getAllResponseHeaders(),
            statusText);
      };

      var requestError = function() {
        // The response is always empty
        // See https://xhr.spec.whatwg.org/#request-error-steps and https://fetch.spec.whatwg.org/#concept-network-error
        completeRequest(callback, -1, null, null, '');
      };

      xhr.onerror = requestError;
      xhr.onabort = requestError;
      xhr.ontimeout = requestError;

      forEach(eventHandlers, function(value, key) {
          xhr.addEventListener(key, value);
      });

      forEach(uploadEventHandlers, function(value, key) {
        xhr.upload.addEventListener(key, value);
      });

      if (withCredentials) {
        xhr.withCredentials = true;
      }

      if (responseType) {
        try {
          xhr.responseType = responseType;
        } catch (e) {
          // WebKit added support for the json responseType value on 09/03/2013
          // https://bugs.webkit.org/show_bug.cgi?id=73648. Versions of Safari prior to 7 are
          // known to throw when setting the value "json" as the response type. Other older
          // browsers implementing the responseType
          //
          // The json response type can be ignored if not supported, because JSON payloads are
          // parsed on the client-side regardless.
          if (responseType !== 'json') {
            throw e;
          }
        }
      }

      xhr.send(isUndefined(post) ? null : post);
    }

    if (timeout > 0) {
      var timeoutId = $browserDefer(timeoutRequest, timeout);
    } else if (isPromiseLike(timeout)) {
      timeout.then(timeoutRequest);
    }


    function timeoutRequest() {
      if (jsonpDone) {
        jsonpDone();
      }
      if (xhr) {
        xhr.abort();
      }
    }

    function completeRequest(callback, status, response, headersString, statusText) {
      // cancel timeout and subsequent timeout promise resolution
      if (isDefined(timeoutId)) {
        $browserDefer.cancel(timeoutId);
      }
      jsonpDone = xhr = null;

      callback(status, response, headersString, statusText);
    }
  };

  function jsonpReq(url, callbackPath, done) {
    url = url.replace('JSON_CALLBACK', callbackPath);
    // we can't use jQuery/jqLite here because jQuery does crazy stuff with script elements, e.g.:
    // - fetches local scripts via XHR and evals them
    // - adds and immediately removes script elements from the document
    var script = rawDocument.createElement('script'), callback = null;
    script.type = 'text/javascript';
    script.src = url;
    script.async = true;

    callback = function(event) {
      script.removeEventListener('load', callback);
      script.removeEventListener('error', callback);
      rawDocument.body.removeChild(script);
      script = null;
      var status = -1;
      var text = 'unknown';

      if (event) {
        if (event.type === 'load' && !callbacks.wasCalled(callbackPath)) {
          event = { type: 'error' };
        }
        text = event.type;
        status = event.type === 'error' ? 404 : 200;
      }

      if (done) {
        done(status, text);
      }
    };

    script.addEventListener('load', callback);
    script.addEventListener('error', callback);
    rawDocument.body.appendChild(script);
    return callback;
  }
}

var $interpolateMinErr = angular.$interpolateMinErr = minErr('$interpolate');
$interpolateMinErr.throwNoconcat = function(text) {
  throw $interpolateMinErr('noconcat',
      'Error while interpolating: {0}\nStrict Contextual Escaping disallows ' +
      'interpolations that concatenate multiple expressions when a trusted value is ' +
      'required.  See http://docs.angularjs.org/api/ng.$sce', text);
};

$interpolateMinErr.interr = function(text, err) {
  return $interpolateMinErr('interr', 'Can\'t interpolate: {0}\n{1}', text, err.toString());
};

/**
 * @ngdoc provider
 * @name $interpolateProvider
 * @this
 *
 * @description
 *
 * Used for configuring the interpolation markup. Defaults to `{{` and `}}`.
 *
 * <div class="alert alert-danger">
 * This feature is sometimes used to mix different markup languages, e.g. to wrap an Angular
 * template within a Python Jinja template (or any other template language). Mixing templating
 * languages is **very dangerous**. The embedding template language will not safely escape Angular
 * expressions, so any user-controlled values in the template will cause Cross Site Scripting (XSS)
 * security bugs!
 * </div>
 *
 * @example
<example name="custom-interpolation-markup" module="customInterpolationApp">
<file name="index.html">
<script>
  var customInterpolationApp = angular.module('customInterpolationApp', []);

  customInterpolationApp.config(function($interpolateProvider) {
    $interpolateProvider.startSymbol('//');
    $interpolateProvider.endSymbol('//');
  });


  customInterpolationApp.controller('DemoController', function() {
      this.label = "This binding is brought you by // interpolation symbols.";
  });
</script>
<div ng-controller="DemoController as demo">
    //demo.label//
</div>
</file>
<file name="protractor.js" type="protractor">
  it('should interpolate binding with custom symbols', function() {
    expect(element(by.binding('demo.label')).getText()).toBe('This binding is brought you by // interpolation symbols.');
  });
</file>
</example>
 */
function $InterpolateProvider() {
  var startSymbol = '{{';
  var endSymbol = '}}';

  /**
   * @ngdoc method
   * @name $interpolateProvider#startSymbol
   * @description
   * Symbol to denote start of expression in the interpolated string. Defaults to `{{`.
   *
   * @param {string=} value new value to set the starting symbol to.
   * @returns {string|self} Returns the symbol when used as getter and self if used as setter.
   */
  this.startSymbol = function(value) {
    if (value) {
      startSymbol = value;
      return this;
    } else {
      return startSymbol;
    }
  };

  /**
   * @ngdoc method
   * @name $interpolateProvider#endSymbol
   * @description
   * Symbol to denote the end of expression in the interpolated string. Defaults to `}}`.
   *
   * @param {string=} value new value to set the ending symbol to.
   * @returns {string|self} Returns the symbol when used as getter and self if used as setter.
   */
  this.endSymbol = function(value) {
    if (value) {
      endSymbol = value;
      return this;
    } else {
      return endSymbol;
    }
  };


  this.$get = ['$parse', '$exceptionHandler', '$sce', function($parse, $exceptionHandler, $sce) {
    var startSymbolLength = startSymbol.length,
        endSymbolLength = endSymbol.length,
        escapedStartRegexp = new RegExp(startSymbol.replace(/./g, escape), 'g'),
        escapedEndRegexp = new RegExp(endSymbol.replace(/./g, escape), 'g');

    function escape(ch) {
      return '\\\\\\' + ch;
    }

    function unescapeText(text) {
      return text.replace(escapedStartRegexp, startSymbol).
        replace(escapedEndRegexp, endSymbol);
    }

    // TODO: this is the same as the constantWatchDelegate in parse.js
    function constantWatchDelegate(scope, listener, objectEquality, constantInterp) {
      var unwatch = scope.$watch(function constantInterpolateWatch(scope) {
        unwatch();
        return constantInterp(scope);
      }, listener, objectEquality);
      return unwatch;
    }

    /**
     * @ngdoc service
     * @name $interpolate
     * @kind function
     *
     * @requires $parse
     * @requires $sce
     *
     * @description
     *
     * Compiles a string with markup into an interpolation function. This service is used by the
     * HTML {@link ng.$compile $compile} service for data binding. See
     * {@link ng.$interpolateProvider $interpolateProvider} for configuring the
     * interpolation markup.
     *
     *
     * ```js
     *   var $interpolate = ...; // injected
     *   var exp = $interpolate('Hello {{name | uppercase}}!');
     *   expect(exp({name:'Angular'})).toEqual('Hello ANGULAR!');
     * ```
     *
     * `$interpolate` takes an optional fourth argument, `allOrNothing`. If `allOrNothing` is
     * `true`, the interpolation function will return `undefined` unless all embedded expressions
     * evaluate to a value other than `undefined`.
     *
     * ```js
     *   var $interpolate = ...; // injected
     *   var context = {greeting: 'Hello', name: undefined };
     *
     *   // default "forgiving" mode
     *   var exp = $interpolate('{{greeting}} {{name}}!');
     *   expect(exp(context)).toEqual('Hello !');
     *
     *   // "allOrNothing" mode
     *   exp = $interpolate('{{greeting}} {{name}}!', false, null, true);
     *   expect(exp(context)).toBeUndefined();
     *   context.name = 'Angular';
     *   expect(exp(context)).toEqual('Hello Angular!');
     * ```
     *
     * `allOrNothing` is useful for interpolating URLs. `ngSrc` and `ngSrcset` use this behavior.
     *
     * #### Escaped Interpolation
     * $interpolate provides a mechanism for escaping interpolation markers. Start and end markers
     * can be escaped by preceding each of their characters with a REVERSE SOLIDUS U+005C (backslash).
     * It will be rendered as a regular start/end marker, and will not be interpreted as an expression
     * or binding.
     *
     * This enables web-servers to prevent script injection attacks and defacing attacks, to some
     * degree, while also enabling code examples to work without relying on the
     * {@link ng.directive:ngNonBindable ngNonBindable} directive.
     *
     * **For security purposes, it is strongly encouraged that web servers escape user-supplied data,
     * replacing angle brackets (&lt;, &gt;) with &amp;lt; and &amp;gt; respectively, and replacing all
     * interpolation start/end markers with their escaped counterparts.**
     *
     * Escaped interpolation markers are only replaced with the actual interpolation markers in rendered
     * output when the $interpolate service processes the text. So, for HTML elements interpolated
     * by {@link ng.$compile $compile}, or otherwise interpolated with the `mustHaveExpression` parameter
     * set to `true`, the interpolated text must contain an unescaped interpolation expression. As such,
     * this is typically useful only when user-data is used in rendering a template from the server, or
     * when otherwise untrusted data is used by a directive.
     *
     * <example name="interpolation">
     *  <file name="index.html">
     *    <div ng-init="username='A user'">
     *      <p ng-init="apptitle='Escaping demo'">{{apptitle}}: \{\{ username = "defaced value"; \}\}
     *        </p>
     *      <p><strong>{{username}}</strong> attempts to inject code which will deface the
     *        application, but fails to accomplish their task, because the server has correctly
     *        escaped the interpolation start/end markers with REVERSE SOLIDUS U+005C (backslash)
     *        characters.</p>
     *      <p>Instead, the result of the attempted script injection is visible, and can be removed
     *        from the database by an administrator.</p>
     *    </div>
     *  </file>
     * </example>
     *
     * @knownIssue
     * It is currently not possible for an interpolated expression to contain the interpolation end
     * symbol. For example, `{{ '}}' }}` will be incorrectly interpreted as `{{ ' }}` + `' }}`, i.e.
     * an interpolated expression consisting of a single-quote (`'`) and the `' }}` string.
     *
     * @knownIssue
     * All directives and components must use the standard `{{` `}}` interpolation symbols
     * in their templates. If you change the application interpolation symbols the {@link $compile}
     * service will attempt to denormalize the standard symbols to the custom symbols.
     * The denormalization process is not clever enough to know not to replace instances of the standard
     * symbols where they would not normally be treated as interpolation symbols. For example in the following
     * code snippet the closing braces of the literal object will get incorrectly denormalized:
     *
     * ```
     * <div data-context='{"context":{"id":3,"type":"page"}}">
     * ```
     *
     * The workaround is to ensure that such instances are separated by whitespace:
     * ```
     * <div data-context='{"context":{"id":3,"type":"page"} }">
     * ```
     *
     * See https://github.com/angular/angular.js/pull/14610#issuecomment-219401099 for more information.
     *
     * @param {string} text The text with markup to interpolate.
     * @param {boolean=} mustHaveExpression if set to true then the interpolation string must have
     *    embedded expression in order to return an interpolation function. Strings with no
     *    embedded expression will return null for the interpolation function.
     * @param {string=} trustedContext when provided, the returned function passes the interpolated
     *    result through {@link ng.$sce#getTrusted $sce.getTrusted(interpolatedResult,
     *    trustedContext)} before returning it.  Refer to the {@link ng.$sce $sce} service that
     *    provides Strict Contextual Escaping for details.
     * @param {boolean=} allOrNothing if `true`, then the returned function returns undefined
     *    unless all embedded expressions evaluate to a value other than `undefined`.
     * @returns {function(context)} an interpolation function which is used to compute the
     *    interpolated string. The function has these parameters:
     *
     * - `context`: evaluation context for all expressions embedded in the interpolated text
     */
    function $interpolate(text, mustHaveExpression, trustedContext, allOrNothing) {
      // Provide a quick exit and simplified result function for text with no interpolation
      if (!text.length || text.indexOf(startSymbol) === -1) {
        var constantInterp;
        if (!mustHaveExpression) {
          var unescapedText = unescapeText(text);
          constantInterp = valueFn(unescapedText);
          constantInterp.exp = text;
          constantInterp.expressions = [];
          constantInterp.$$watchDelegate = constantWatchDelegate;
        }
        return constantInterp;
      }

      allOrNothing = !!allOrNothing;
      var startIndex,
          endIndex,
          index = 0,
          expressions = [],
          parseFns = [],
          textLength = text.length,
          exp,
          concat = [],
          expressionPositions = [];

      while (index < textLength) {
        if (((startIndex = text.indexOf(startSymbol, index)) !== -1) &&
             ((endIndex = text.indexOf(endSymbol, startIndex + startSymbolLength)) !== -1)) {
          if (index !== startIndex) {
            concat.push(unescapeText(text.substring(index, startIndex)));
          }
          exp = text.substring(startIndex + startSymbolLength, endIndex);
          expressions.push(exp);
          parseFns.push($parse(exp, parseStringifyInterceptor));
          index = endIndex + endSymbolLength;
          expressionPositions.push(concat.length);
          concat.push('');
        } else {
          // we did not find an interpolation, so we have to add the remainder to the separators array
          if (index !== textLength) {
            concat.push(unescapeText(text.substring(index)));
          }
          break;
        }
      }

      // Concatenating expressions makes it hard to reason about whether some combination of
      // concatenated values are unsafe to use and could easily lead to XSS.  By requiring that a
      // single expression be used for iframe[src], object[src], etc., we ensure that the value
      // that's used is assigned or constructed by some JS code somewhere that is more testable or
      // make it obvious that you bound the value to some user controlled value.  This helps reduce
      // the load when auditing for XSS issues.
      if (trustedContext && concat.length > 1) {
          $interpolateMinErr.throwNoconcat(text);
      }

      if (!mustHaveExpression || expressions.length) {
        var compute = function(values) {
          for (var i = 0, ii = expressions.length; i < ii; i++) {
            if (allOrNothing && isUndefined(values[i])) return;
            concat[expressionPositions[i]] = values[i];
          }
          return concat.join('');
        };

        var getValue = function(value) {
          return trustedContext ?
            $sce.getTrusted(trustedContext, value) :
            $sce.valueOf(value);
        };

        return extend(function interpolationFn(context) {
            var i = 0;
            var ii = expressions.length;
            var values = new Array(ii);

            try {
              for (; i < ii; i++) {
                values[i] = parseFns[i](context);
              }

              return compute(values);
            } catch (err) {
              $exceptionHandler($interpolateMinErr.interr(text, err));
            }

          }, {
          // all of these properties are undocumented for now
          exp: text, //just for compatibility with regular watchers created via $watch
          expressions: expressions,
          $$watchDelegate: function(scope, listener) {
            var lastValue;
            return scope.$watchGroup(parseFns, /** @this */ function interpolateFnWatcher(values, oldValues) {
              var currValue = compute(values);
              if (isFunction(listener)) {
                listener.call(this, currValue, values !== oldValues ? lastValue : currValue, scope);
              }
              lastValue = currValue;
            });
          }
        });
      }

      function parseStringifyInterceptor(value) {
        try {
          value = getValue(value);
          return allOrNothing && !isDefined(value) ? value : stringify(value);
        } catch (err) {
          $exceptionHandler($interpolateMinErr.interr(text, err));
        }
      }
    }


    /**
     * @ngdoc method
     * @name $interpolate#startSymbol
     * @description
     * Symbol to denote the start of expression in the interpolated string. Defaults to `{{`.
     *
     * Use {@link ng.$interpolateProvider#startSymbol `$interpolateProvider.startSymbol`} to change
     * the symbol.
     *
     * @returns {string} start symbol.
     */
    $interpolate.startSymbol = function() {
      return startSymbol;
    };


    /**
     * @ngdoc method
     * @name $interpolate#endSymbol
     * @description
     * Symbol to denote the end of expression in the interpolated string. Defaults to `}}`.
     *
     * Use {@link ng.$interpolateProvider#endSymbol `$interpolateProvider.endSymbol`} to change
     * the symbol.
     *
     * @returns {string} end symbol.
     */
    $interpolate.endSymbol = function() {
      return endSymbol;
    };

    return $interpolate;
  }];
}

/** @this */
function $IntervalProvider() {
  this.$get = ['$rootScope', '$window', '$q', '$$q', '$browser',
       function($rootScope,   $window,   $q,   $$q,   $browser) {
    var intervals = {};


     /**
      * @ngdoc service
      * @name $interval
      *
      * @description
      * Angular's wrapper for `window.setInterval`. The `fn` function is executed every `delay`
      * milliseconds.
      *
      * The return value of registering an interval function is a promise. This promise will be
      * notified upon each tick of the interval, and will be resolved after `count` iterations, or
      * run indefinitely if `count` is not defined. The value of the notification will be the
      * number of iterations that have run.
      * To cancel an interval, call `$interval.cancel(promise)`.
      *
      * In tests you can use {@link ngMock.$interval#flush `$interval.flush(millis)`} to
      * move forward by `millis` milliseconds and trigger any functions scheduled to run in that
      * time.
      *
      * <div class="alert alert-warning">
      * **Note**: Intervals created by this service must be explicitly destroyed when you are finished
      * with them.  In particular they are not automatically destroyed when a controller's scope or a
      * directive's element are destroyed.
      * You should take this into consideration and make sure to always cancel the interval at the
      * appropriate moment.  See the example below for more details on how and when to do this.
      * </div>
      *
      * @param {function()} fn A function that should be called repeatedly. If no additional arguments
      *   are passed (see below), the function is called with the current iteration count.
      * @param {number} delay Number of milliseconds between each function call.
      * @param {number=} [count=0] Number of times to repeat. If not set, or 0, will repeat
      *   indefinitely.
      * @param {boolean=} [invokeApply=true] If set to `false` skips model dirty checking, otherwise
      *   will invoke `fn` within the {@link ng.$rootScope.Scope#$apply $apply} block.
      * @param {...*=} Pass additional parameters to the executed function.
      * @returns {promise} A promise which will be notified on each iteration.
      *
      * @example
      * <example module="intervalExample" name="interval-service">
      * <file name="index.html">
      *   <script>
      *     angular.module('intervalExample', [])
      *       .controller('ExampleController', ['$scope', '$interval',
      *         function($scope, $interval) {
      *           $scope.format = 'M/d/yy h:mm:ss a';
      *           $scope.blood_1 = 100;
      *           $scope.blood_2 = 120;
      *
      *           var stop;
      *           $scope.fight = function() {
      *             // Don't start a new fight if we are already fighting
      *             if ( angular.isDefined(stop) ) return;
      *
      *             stop = $interval(function() {
      *               if ($scope.blood_1 > 0 && $scope.blood_2 > 0) {
      *                 $scope.blood_1 = $scope.blood_1 - 3;
      *                 $scope.blood_2 = $scope.blood_2 - 4;
      *               } else {
      *                 $scope.stopFight();
      *               }
      *             }, 100);
      *           };
      *
      *           $scope.stopFight = function() {
      *             if (angular.isDefined(stop)) {
      *               $interval.cancel(stop);
      *               stop = undefined;
      *             }
      *           };
      *
      *           $scope.resetFight = function() {
      *             $scope.blood_1 = 100;
      *             $scope.blood_2 = 120;
      *           };
      *
      *           $scope.$on('$destroy', function() {
      *             // Make sure that the interval is destroyed too
      *             $scope.stopFight();
      *           });
      *         }])
      *       // Register the 'myCurrentTime' directive factory method.
      *       // We inject $interval and dateFilter service since the factory method is DI.
      *       .directive('myCurrentTime', ['$interval', 'dateFilter',
      *         function($interval, dateFilter) {
      *           // return the directive link function. (compile function not needed)
      *           return function(scope, element, attrs) {
      *             var format,  // date format
      *                 stopTime; // so that we can cancel the time updates
      *
      *             // used to update the UI
      *             function updateTime() {
      *               element.text(dateFilter(new Date(), format));
      *             }
      *
      *             // watch the expression, and update the UI on change.
      *             scope.$watch(attrs.myCurrentTime, function(value) {
      *               format = value;
      *               updateTime();
      *             });
      *
      *             stopTime = $interval(updateTime, 1000);
      *
      *             // listen on DOM destroy (removal) event, and cancel the next UI update
      *             // to prevent updating time after the DOM element was removed.
      *             element.on('$destroy', function() {
      *               $interval.cancel(stopTime);
      *             });
      *           }
      *         }]);
      *   </script>
      *
      *   <div>
      *     <div ng-controller="ExampleController">
      *       <label>Date format: <input ng-model="format"></label> <hr/>
      *       Current time is: <span my-current-time="format"></span>
      *       <hr/>
      *       Blood 1 : <font color='red'>{{blood_1}}</font>
      *       Blood 2 : <font color='red'>{{blood_2}}</font>
      *       <button type="button" data-ng-click="fight()">Fight</button>
      *       <button type="button" data-ng-click="stopFight()">StopFight</button>
      *       <button type="button" data-ng-click="resetFight()">resetFight</button>
      *     </div>
      *   </div>
      *
      * </file>
      * </example>
      */
    function interval(fn, delay, count, invokeApply) {
      var hasParams = arguments.length > 4,
          args = hasParams ? sliceArgs(arguments, 4) : [],
          setInterval = $window.setInterval,
          clearInterval = $window.clearInterval,
          iteration = 0,
          skipApply = (isDefined(invokeApply) && !invokeApply),
          deferred = (skipApply ? $$q : $q).defer(),
          promise = deferred.promise;

      count = isDefined(count) ? count : 0;

      promise.$$intervalId = setInterval(function tick() {
        if (skipApply) {
          $browser.defer(callback);
        } else {
          $rootScope.$evalAsync(callback);
        }
        deferred.notify(iteration++);

        if (count > 0 && iteration >= count) {
          deferred.resolve(iteration);
          clearInterval(promise.$$intervalId);
          delete intervals[promise.$$intervalId];
        }

        if (!skipApply) $rootScope.$apply();

      }, delay);

      intervals[promise.$$intervalId] = deferred;

      return promise;

      function callback() {
        if (!hasParams) {
          fn(iteration);
        } else {
          fn.apply(null, args);
        }
      }
    }


     /**
      * @ngdoc method
      * @name $interval#cancel
      *
      * @description
      * Cancels a task associated with the `promise`.
      *
      * @param {Promise=} promise returned by the `$interval` function.
      * @returns {boolean} Returns `true` if the task was successfully canceled.
      */
    interval.cancel = function(promise) {
      if (promise && promise.$$intervalId in intervals) {
        // Interval cancels should not report as unhandled promise.
        intervals[promise.$$intervalId].promise.catch(noop);
        intervals[promise.$$intervalId].reject('canceled');
        $window.clearInterval(promise.$$intervalId);
        delete intervals[promise.$$intervalId];
        return true;
      }
      return false;
    };

    return interval;
  }];
}

/**
 * @ngdoc service
 * @name $jsonpCallbacks
 * @requires $window
 * @description
 * This service handles the lifecycle of callbacks to handle JSONP requests.
 * Override this service if you wish to customise where the callbacks are stored and
 * how they vary compared to the requested url.
 */
var $jsonpCallbacksProvider = /** @this */ function() {
  this.$get = function() {
    var callbacks = angular.callbacks;
    var callbackMap = {};

    function createCallback(callbackId) {
      var callback = function(data) {
        callback.data = data;
        callback.called = true;
      };
      callback.id = callbackId;
      return callback;
    }

    return {
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#createCallback
       * @param {string} url the url of the JSONP request
       * @returns {string} the callback path to send to the server as part of the JSONP request
       * @description
       * {@link $httpBackend} calls this method to create a callback and get hold of the path to the callback
       * to pass to the server, which will be used to call the callback with its payload in the JSONP response.
       */
      createCallback: function(url) {
        var callbackId = '_' + (callbacks.$$counter++).toString(36);
        var callbackPath = 'angular.callbacks.' + callbackId;
        var callback = createCallback(callbackId);
        callbackMap[callbackPath] = callbacks[callbackId] = callback;
        return callbackPath;
      },
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#wasCalled
       * @param {string} callbackPath the path to the callback that was sent in the JSONP request
       * @returns {boolean} whether the callback has been called, as a result of the JSONP response
       * @description
       * {@link $httpBackend} calls this method to find out whether the JSONP response actually called the
       * callback that was passed in the request.
       */
      wasCalled: function(callbackPath) {
        return callbackMap[callbackPath].called;
      },
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#getResponse
       * @param {string} callbackPath the path to the callback that was sent in the JSONP request
       * @returns {*} the data received from the response via the registered callback
       * @description
       * {@link $httpBackend} calls this method to get hold of the data that was provided to the callback
       * in the JSONP response.
       */
      getResponse: function(callbackPath) {
        return callbackMap[callbackPath].data;
      },
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#removeCallback
       * @param {string} callbackPath the path to the callback that was sent in the JSONP request
       * @description
       * {@link $httpBackend} calls this method to remove the callback after the JSONP request has
       * completed or timed-out.
       */
      removeCallback: function(callbackPath) {
        var callback = callbackMap[callbackPath];
        delete callbacks[callback.id];
        delete callbackMap[callbackPath];
      }
    };
  };
};

/**
 * @ngdoc service
 * @name $locale
 *
 * @description
 * $locale service provides localization rules for various Angular components. As of right now the
 * only public api is:
 *
 * * `id` – `{string}` – locale id formatted as `languageId-countryId` (e.g. `en-us`)
 */

var PATH_MATCH = /^([^?#]*)(\?([^#]*))?(#(.*))?$/,
    DEFAULT_PORTS = {'http': 80, 'https': 443, 'ftp': 21};
var $locationMinErr = minErr('$location');


/**
 * Encode path using encodeUriSegment, ignoring forward slashes
 *
 * @param {string} path Path to encode
 * @returns {string}
 */
function encodePath(path) {
  var segments = path.split('/'),
      i = segments.length;

  while (i--) {
    segments[i] = encodeUriSegment(segments[i]);
  }

  return segments.join('/');
}

function parseAbsoluteUrl(absoluteUrl, locationObj) {
  var parsedUrl = urlResolve(absoluteUrl);

  locationObj.$$protocol = parsedUrl.protocol;
  locationObj.$$host = parsedUrl.hostname;
  locationObj.$$port = toInt(parsedUrl.port) || DEFAULT_PORTS[parsedUrl.protocol] || null;
}

var DOUBLE_SLASH_REGEX = /^\s*[\\/]{2,}/;
function parseAppUrl(url, locationObj) {

  if (DOUBLE_SLASH_REGEX.test(url)) {
    throw $locationMinErr('badpath', 'Invalid url "{0}".', url);
  }

  var prefixed = (url.charAt(0) !== '/');
  if (prefixed) {
    url = '/' + url;
  }
  var match = urlResolve(url);
  locationObj.$$path = decodeURIComponent(prefixed && match.pathname.charAt(0) === '/' ?
      match.pathname.substring(1) : match.pathname);
  locationObj.$$search = parseKeyValue(match.search);
  locationObj.$$hash = decodeURIComponent(match.hash);

  // make sure path starts with '/';
  if (locationObj.$$path && locationObj.$$path.charAt(0) !== '/') {
    locationObj.$$path = '/' + locationObj.$$path;
  }
}

function startsWith(str, search) {
  return str.slice(0, search.length) === search;
}

/**
 *
 * @param {string} base
 * @param {string} url
 * @returns {string} returns text from `url` after `base` or `undefined` if it does not begin with
 *                   the expected string.
 */
function stripBaseUrl(base, url) {
  if (startsWith(url, base)) {
    return url.substr(base.length);
  }
}


function stripHash(url) {
  var index = url.indexOf('#');
  return index === -1 ? url : url.substr(0, index);
}

function trimEmptyHash(url) {
  return url.replace(/(#.+)|#$/, '$1');
}


function stripFile(url) {
  return url.substr(0, stripHash(url).lastIndexOf('/') + 1);
}

/* return the server only (scheme://host:port) */
function serverBase(url) {
  return url.substring(0, url.indexOf('/', url.indexOf('//') + 2));
}


/**
 * LocationHtml5Url represents a URL
 * This object is exposed as $location service when HTML5 mode is enabled and supported
 *
 * @constructor
 * @param {string} appBase application base URL
 * @param {string} appBaseNoFile application base URL stripped of any filename
 * @param {string} basePrefix URL path prefix
 */
function LocationHtml5Url(appBase, appBaseNoFile, basePrefix) {
  this.$$html5 = true;
  basePrefix = basePrefix || '';
  parseAbsoluteUrl(appBase, this);


  /**
   * Parse given HTML5 (regular) URL string into properties
   * @param {string} url HTML5 URL
   * @private
   */
  this.$$parse = function(url) {
    var pathUrl = stripBaseUrl(appBaseNoFile, url);
    if (!isString(pathUrl)) {
      throw $locationMinErr('ipthprfx', 'Invalid url "{0}", missing path prefix "{1}".', url,
          appBaseNoFile);
    }

    parseAppUrl(pathUrl, this);

    if (!this.$$path) {
      this.$$path = '/';
    }

    this.$$compose();
  };

  /**
   * Compose url and update `absUrl` property
   * @private
   */
  this.$$compose = function() {
    var search = toKeyValue(this.$$search),
        hash = this.$$hash ? '#' + encodeUriSegment(this.$$hash) : '';

    this.$$url = encodePath(this.$$path) + (search ? '?' + search : '') + hash;
    this.$$absUrl = appBaseNoFile + this.$$url.substr(1); // first char is always '/'

    this.$$urlUpdatedByLocation = true;
  };

  this.$$parseLinkUrl = function(url, relHref) {
    if (relHref && relHref[0] === '#') {
      // special case for links to hash fragments:
      // keep the old url and only replace the hash fragment
      this.hash(relHref.slice(1));
      return true;
    }
    var appUrl, prevAppUrl;
    var rewrittenUrl;


    if (isDefined(appUrl = stripBaseUrl(appBase, url))) {
      prevAppUrl = appUrl;
      if (basePrefix && isDefined(appUrl = stripBaseUrl(basePrefix, appUrl))) {
        rewrittenUrl = appBaseNoFile + (stripBaseUrl('/', appUrl) || appUrl);
      } else {
        rewrittenUrl = appBase + prevAppUrl;
      }
    } else if (isDefined(appUrl = stripBaseUrl(appBaseNoFile, url))) {
      rewrittenUrl = appBaseNoFile + appUrl;
    } else if (appBaseNoFile === url + '/') {
      rewrittenUrl = appBaseNoFile;
    }
    if (rewrittenUrl) {
      this.$$parse(rewrittenUrl);
    }
    return !!rewrittenUrl;
  };
}


/**
 * LocationHashbangUrl represents URL
 * This object is exposed as $location service when developer doesn't opt into html5 mode.
 * It also serves as the base class for html5 mode fallback on legacy browsers.
 *
 * @constructor
 * @param {string} appBase application base URL
 * @param {string} appBaseNoFile application base URL stripped of any filename
 * @param {string} hashPrefix hashbang prefix
 */
function LocationHashbangUrl(appBase, appBaseNoFile, hashPrefix) {

  parseAbsoluteUrl(appBase, this);


  /**
   * Parse given hashbang URL into properties
   * @param {string} url Hashbang URL
   * @private
   */
  this.$$parse = function(url) {
    var withoutBaseUrl = stripBaseUrl(appBase, url) || stripBaseUrl(appBaseNoFile, url);
    var withoutHashUrl;

    if (!isUndefined(withoutBaseUrl) && withoutBaseUrl.charAt(0) === '#') {

      // The rest of the URL starts with a hash so we have
      // got either a hashbang path or a plain hash fragment
      withoutHashUrl = stripBaseUrl(hashPrefix, withoutBaseUrl);
      if (isUndefined(withoutHashUrl)) {
        // There was no hashbang prefix so we just have a hash fragment
        withoutHashUrl = withoutBaseUrl;
      }

    } else {
      // There was no hashbang path nor hash fragment:
      // If we are in HTML5 mode we use what is left as the path;
      // Otherwise we ignore what is left
      if (this.$$html5) {
        withoutHashUrl = withoutBaseUrl;
      } else {
        withoutHashUrl = '';
        if (isUndefined(withoutBaseUrl)) {
          appBase = url;
          /** @type {?} */ (this).replace();
        }
      }
    }

    parseAppUrl(withoutHashUrl, this);

    this.$$path = removeWindowsDriveName(this.$$path, withoutHashUrl, appBase);

    this.$$compose();

    /*
     * In Windows, on an anchor node on documents loaded from
     * the filesystem, the browser will return a pathname
     * prefixed with the drive name ('/C:/path') when a
     * pathname without a drive is set:
     *  * a.setAttribute('href', '/foo')
     *   * a.pathname === '/C:/foo' //true
     *
     * Inside of Angular, we're always using pathnames that
     * do not include drive names for routing.
     */
    function removeWindowsDriveName(path, url, base) {
      /*
      Matches paths for file protocol on windows,
      such as /C:/foo/bar, and captures only /foo/bar.
      */
      var windowsFilePathExp = /^\/[A-Z]:(\/.*)/;

      var firstPathSegmentMatch;

      //Get the relative path from the input URL.
      if (startsWith(url, base)) {
        url = url.replace(base, '');
      }

      // The input URL intentionally contains a first path segment that ends with a colon.
      if (windowsFilePathExp.exec(url)) {
        return path;
      }

      firstPathSegmentMatch = windowsFilePathExp.exec(path);
      return firstPathSegmentMatch ? firstPathSegmentMatch[1] : path;
    }
  };

  /**
   * Compose hashbang URL and update `absUrl` property
   * @private
   */
  this.$$compose = function() {
    var search = toKeyValue(this.$$search),
        hash = this.$$hash ? '#' + encodeUriSegment(this.$$hash) : '';

    this.$$url = encodePath(this.$$path) + (search ? '?' + search : '') + hash;
    this.$$absUrl = appBase + (this.$$url ? hashPrefix + this.$$url : '');

    this.$$urlUpdatedByLocation = true;
  };

  this.$$parseLinkUrl = function(url, relHref) {
    if (stripHash(appBase) === stripHash(url)) {
      this.$$parse(url);
      return true;
    }
    return false;
  };
}


/**
 * LocationHashbangUrl represents URL
 * This object is exposed as $location service when html5 history api is enabled but the browser
 * does not support it.
 *
 * @constructor
 * @param {string} appBase application base URL
 * @param {string} appBaseNoFile application base URL stripped of any filename
 * @param {string} hashPrefix hashbang prefix
 */
function LocationHashbangInHtml5Url(appBase, appBaseNoFile, hashPrefix) {
  this.$$html5 = true;
  LocationHashbangUrl.apply(this, arguments);

  this.$$parseLinkUrl = function(url, relHref) {
    if (relHref && relHref[0] === '#') {
      // special case for links to hash fragments:
      // keep the old url and only replace the hash fragment
      this.hash(relHref.slice(1));
      return true;
    }

    var rewrittenUrl;
    var appUrl;

    if (appBase === stripHash(url)) {
      rewrittenUrl = url;
    } else if ((appUrl = stripBaseUrl(appBaseNoFile, url))) {
      rewrittenUrl = appBase + hashPrefix + appUrl;
    } else if (appBaseNoFile === url + '/') {
      rewrittenUrl = appBaseNoFile;
    }
    if (rewrittenUrl) {
      this.$$parse(rewrittenUrl);
    }
    return !!rewrittenUrl;
  };

  this.$$compose = function() {
    var search = toKeyValue(this.$$search),
        hash = this.$$hash ? '#' + encodeUriSegment(this.$$hash) : '';

    this.$$url = encodePath(this.$$path) + (search ? '?' + search : '') + hash;
    // include hashPrefix in $$absUrl when $$url is empty so IE9 does not reload page because of removal of '#'
    this.$$absUrl = appBase + hashPrefix + this.$$url;

    this.$$urlUpdatedByLocation = true;
  };

}


var locationPrototype = {

  /**
   * Ensure absolute URL is initialized.
   * @private
   */
  $$absUrl:'',

  /**
   * Are we in html5 mode?
   * @private
   */
  $$html5: false,

  /**
   * Has any change been replacing?
   * @private
   */
  $$replace: false,

  /**
   * @ngdoc method
   * @name $location#absUrl
   *
   * @description
   * This method is getter only.
   *
   * Return full URL representation with all segments encoded according to rules specified in
   * [RFC 3986](http://www.ietf.org/rfc/rfc3986.txt).
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var absUrl = $location.absUrl();
   * // => "http://example.com/#/some/path?foo=bar&baz=xoxo"
   * ```
   *
   * @return {string} full URL
   */
  absUrl: locationGetter('$$absUrl'),

  /**
   * @ngdoc method
   * @name $location#url
   *
   * @description
   * This method is getter / setter.
   *
   * Return URL (e.g. `/path?a=b#hash`) when called without any parameter.
   *
   * Change path, search and hash, when called with parameter and return `$location`.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var url = $location.url();
   * // => "/some/path?foo=bar&baz=xoxo"
   * ```
   *
   * @param {string=} url New URL without base prefix (e.g. `/path?a=b#hash`)
   * @return {string} url
   */
  url: function(url) {
    if (isUndefined(url)) {
      return this.$$url;
    }

    var match = PATH_MATCH.exec(url);
    if (match[1] || url === '') this.path(decodeURIComponent(match[1]));
    if (match[2] || match[1] || url === '') this.search(match[3] || '');
    this.hash(match[5] || '');

    return this;
  },

  /**
   * @ngdoc method
   * @name $location#protocol
   *
   * @description
   * This method is getter only.
   *
   * Return protocol of current URL.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var protocol = $location.protocol();
   * // => "http"
   * ```
   *
   * @return {string} protocol of current URL
   */
  protocol: locationGetter('$$protocol'),

  /**
   * @ngdoc method
   * @name $location#host
   *
   * @description
   * This method is getter only.
   *
   * Return host of current URL.
   *
   * Note: compared to the non-angular version `location.host` which returns `hostname:port`, this returns the `hostname` portion only.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var host = $location.host();
   * // => "example.com"
   *
   * // given URL http://user:password@example.com:8080/#/some/path?foo=bar&baz=xoxo
   * host = $location.host();
   * // => "example.com"
   * host = location.host;
   * // => "example.com:8080"
   * ```
   *
   * @return {string} host of current URL.
   */
  host: locationGetter('$$host'),

  /**
   * @ngdoc method
   * @name $location#port
   *
   * @description
   * This method is getter only.
   *
   * Return port of current URL.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var port = $location.port();
   * // => 80
   * ```
   *
   * @return {Number} port
   */
  port: locationGetter('$$port'),

  /**
   * @ngdoc method
   * @name $location#path
   *
   * @description
   * This method is getter / setter.
   *
   * Return path of current URL when called without any parameter.
   *
   * Change path when called with parameter and return `$location`.
   *
   * Note: Path should always begin with forward slash (/), this method will add the forward slash
   * if it is missing.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var path = $location.path();
   * // => "/some/path"
   * ```
   *
   * @param {(string|number)=} path New path
   * @return {(string|object)} path if called with no parameters, or `$location` if called with a parameter
   */
  path: locationGetterSetter('$$path', function(path) {
    path = path !== null ? path.toString() : '';
    return path.charAt(0) === '/' ? path : '/' + path;
  }),

  /**
   * @ngdoc method
   * @name $location#search
   *
   * @description
   * This method is getter / setter.
   *
   * Return search part (as object) of current URL when called without any parameter.
   *
   * Change search part when called with parameter and return `$location`.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var searchObject = $location.search();
   * // => {foo: 'bar', baz: 'xoxo'}
   *
   * // set foo to 'yipee'
   * $location.search('foo', 'yipee');
   * // $location.search() => {foo: 'yipee', baz: 'xoxo'}
   * ```
   *
   * @param {string|Object.<string>|Object.<Array.<string>>} search New search params - string or
   * hash object.
   *
   * When called with a single argument the method acts as a setter, setting the `search` component
   * of `$location` to the specified value.
   *
   * If the argument is a hash object containing an array of values, these values will be encoded
   * as duplicate search parameters in the URL.
   *
   * @param {(string|Number|Array<string>|boolean)=} paramValue If `search` is a string or number, then `paramValue`
   * will override only a single search property.
   *
   * If `paramValue` is an array, it will override the property of the `search` component of
   * `$location` specified via the first argument.
   *
   * If `paramValue` is `null`, the property specified via the first argument will be deleted.
   *
   * If `paramValue` is `true`, the property specified via the first argument will be added with no
   * value nor trailing equal sign.
   *
   * @return {Object} If called with no arguments returns the parsed `search` object. If called with
   * one or more arguments returns `$location` object itself.
   */
  search: function(search, paramValue) {
    switch (arguments.length) {
      case 0:
        return this.$$search;
      case 1:
        if (isString(search) || isNumber(search)) {
          search = search.toString();
          this.$$search = parseKeyValue(search);
        } else if (isObject(search)) {
          search = copy(search, {});
          // remove object undefined or null properties
          forEach(search, function(value, key) {
            if (value == null) delete search[key];
          });

          this.$$search = search;
        } else {
          throw $locationMinErr('isrcharg',
              'The first argument of the `$location#search()` call must be a string or an object.');
        }
        break;
      default:
        if (isUndefined(paramValue) || paramValue === null) {
          delete this.$$search[search];
        } else {
          this.$$search[search] = paramValue;
        }
    }

    this.$$compose();
    return this;
  },

  /**
   * @ngdoc method
   * @name $location#hash
   *
   * @description
   * This method is getter / setter.
   *
   * Returns the hash fragment when called without any parameters.
   *
   * Changes the hash fragment when called with a parameter and returns `$location`.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo#hashValue
   * var hash = $location.hash();
   * // => "hashValue"
   * ```
   *
   * @param {(string|number)=} hash New hash fragment
   * @return {string} hash
   */
  hash: locationGetterSetter('$$hash', function(hash) {
    return hash !== null ? hash.toString() : '';
  }),

  /**
   * @ngdoc method
   * @name $location#replace
   *
   * @description
   * If called, all changes to $location during the current `$digest` will replace the current history
   * record, instead of adding a new one.
   */
  replace: function() {
    this.$$replace = true;
    return this;
  }
};

forEach([LocationHashbangInHtml5Url, LocationHashbangUrl, LocationHtml5Url], function(Location) {
  Location.prototype = Object.create(locationPrototype);

  /**
   * @ngdoc method
   * @name $location#state
   *
   * @description
   * This method is getter / setter.
   *
   * Return the history state object when called without any parameter.
   *
   * Change the history state object when called with one parameter and return `$location`.
   * The state object is later passed to `pushState` or `replaceState`.
   *
   * NOTE: This method is supported only in HTML5 mode and only in browsers supporting
   * the HTML5 History API (i.e. methods `pushState` and `replaceState`). If you need to support
   * older browsers (like IE9 or Android < 4.0), don't use this method.
   *
   * @param {object=} state State object for pushState or replaceState
   * @return {object} state
   */
  Location.prototype.state = function(state) {
    if (!arguments.length) {
      return this.$$state;
    }

    if (Location !== LocationHtml5Url || !this.$$html5) {
      throw $locationMinErr('nostate', 'History API state support is available only ' +
        'in HTML5 mode and only in browsers supporting HTML5 History API');
    }
    // The user might modify `stateObject` after invoking `$location.state(stateObject)`
    // but we're changing the $$state reference to $browser.state() during the $digest
    // so the modification window is narrow.
    this.$$state = isUndefined(state) ? null : state;
    this.$$urlUpdatedByLocation = true;

    return this;
  };
});


function locationGetter(property) {
  return /** @this */ function() {
    return this[property];
  };
}


function locationGetterSetter(property, preprocess) {
  return /** @this */ function(value) {
    if (isUndefined(value)) {
      return this[property];
    }

    this[property] = preprocess(value);
    this.$$compose();

    return this;
  };
}


/**
 * @ngdoc service
 * @name $location
 *
 * @requires $rootElement
 *
 * @description
 * The $location service parses the URL in the browser address bar (based on the
 * [window.location](https://developer.mozilla.org/en/window.location)) and makes the URL
 * available to your application. Changes to the URL in the address bar are reflected into
 * $location service and changes to $location are reflected into the browser address bar.
 *
 * **The $location service:**
 *
 * - Exposes the current URL in the browser address bar, so you can
 *   - Watch and observe the URL.
 *   - Change the URL.
 * - Synchronizes the URL with the browser when the user
 *   - Changes the address bar.
 *   - Clicks the back or forward button (or clicks a History link).
 *   - Clicks on a link.
 * - Represents the URL object as a set of methods (protocol, host, port, path, search, hash).
 *
 * For more information see {@link guide/$location Developer Guide: Using $location}
 */

/**
 * @ngdoc provider
 * @name $locationProvider
 * @this
 *
 * @description
 * Use the `$locationProvider` to configure how the application deep linking paths are stored.
 */
function $LocationProvider() {
  var hashPrefix = '!',
      html5Mode = {
        enabled: false,
        requireBase: true,
        rewriteLinks: true
      };

  /**
   * @ngdoc method
   * @name $locationProvider#hashPrefix
   * @description
   * The default value for the prefix is `'!'`.
   * @param {string=} prefix Prefix for hash part (containing path and search)
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   */
  this.hashPrefix = function(prefix) {
    if (isDefined(prefix)) {
      hashPrefix = prefix;
      return this;
    } else {
      return hashPrefix;
    }
  };

  /**
   * @ngdoc method
   * @name $locationProvider#html5Mode
   * @description
   * @param {(boolean|Object)=} mode If boolean, sets `html5Mode.enabled` to value.
   *   If object, sets `enabled`, `requireBase` and `rewriteLinks` to respective values. Supported
   *   properties:
   *   - **enabled** – `{boolean}` – (default: false) If true, will rely on `history.pushState` to
   *     change urls where supported. Will fall back to hash-prefixed paths in browsers that do not
   *     support `pushState`.
   *   - **requireBase** - `{boolean}` - (default: `true`) When html5Mode is enabled, specifies
   *     whether or not a <base> tag is required to be present. If `enabled` and `requireBase` are
   *     true, and a base tag is not present, an error will be thrown when `$location` is injected.
   *     See the {@link guide/$location $location guide for more information}
   *   - **rewriteLinks** - `{boolean|string}` - (default: `true`) When html5Mode is enabled,
   *     enables/disables URL rewriting for relative links. If set to a string, URL rewriting will
   *     only happen on links with an attribute that matches the given string. For example, if set
   *     to `'internal-link'`, then the URL will only be rewritten for `<a internal-link>` links.
   *     Note that [attribute name normalization](guide/directive#normalization) does not apply
   *     here, so `'internalLink'` will **not** match `'internal-link'`.
   *
   * @returns {Object} html5Mode object if used as getter or itself (chaining) if used as setter
   */
  this.html5Mode = function(mode) {
    if (isBoolean(mode)) {
      html5Mode.enabled = mode;
      return this;
    } else if (isObject(mode)) {

      if (isBoolean(mode.enabled)) {
        html5Mode.enabled = mode.enabled;
      }

      if (isBoolean(mode.requireBase)) {
        html5Mode.requireBase = mode.requireBase;
      }

      if (isBoolean(mode.rewriteLinks) || isString(mode.rewriteLinks)) {
        html5Mode.rewriteLinks = mode.rewriteLinks;
      }

      return this;
    } else {
      return html5Mode;
    }
  };

  /**
   * @ngdoc event
   * @name $location#$locationChangeStart
   * @eventType broadcast on root scope
   * @description
   * Broadcasted before a URL will change.
   *
   * This change can be prevented by calling
   * `preventDefault` method of the event. See {@link ng.$rootScope.Scope#$on} for more
   * details about event object. Upon successful change
   * {@link ng.$location#$locationChangeSuccess $locationChangeSuccess} is fired.
   *
   * The `newState` and `oldState` parameters may be defined only in HTML5 mode and when
   * the browser supports the HTML5 History API.
   *
   * @param {Object} angularEvent Synthetic event object.
   * @param {string} newUrl New URL
   * @param {string=} oldUrl URL that was before it was changed.
   * @param {string=} newState New history state object
   * @param {string=} oldState History state object that was before it was changed.
   */

  /**
   * @ngdoc event
   * @name $location#$locationChangeSuccess
   * @eventType broadcast on root scope
   * @description
   * Broadcasted after a URL was changed.
   *
   * The `newState` and `oldState` parameters may be defined only in HTML5 mode and when
   * the browser supports the HTML5 History API.
   *
   * @param {Object} angularEvent Synthetic event object.
   * @param {string} newUrl New URL
   * @param {string=} oldUrl URL that was before it was changed.
   * @param {string=} newState New history state object
   * @param {string=} oldState History state object that was before it was changed.
   */

  this.$get = ['$rootScope', '$browser', '$sniffer', '$rootElement', '$window',
      function($rootScope, $browser, $sniffer, $rootElement, $window) {
    var $location,
        LocationMode,
        baseHref = $browser.baseHref(), // if base[href] is undefined, it defaults to ''
        initialUrl = $browser.url(),
        appBase;

    if (html5Mode.enabled) {
      if (!baseHref && html5Mode.requireBase) {
        throw $locationMinErr('nobase',
          '$location in HTML5 mode requires a <base> tag to be present!');
      }
      appBase = serverBase(initialUrl) + (baseHref || '/');
      LocationMode = $sniffer.history ? LocationHtml5Url : LocationHashbangInHtml5Url;
    } else {
      appBase = stripHash(initialUrl);
      LocationMode = LocationHashbangUrl;
    }
    var appBaseNoFile = stripFile(appBase);

    $location = new LocationMode(appBase, appBaseNoFile, '#' + hashPrefix);
    $location.$$parseLinkUrl(initialUrl, initialUrl);

    $location.$$state = $browser.state();

    var IGNORE_URI_REGEXP = /^\s*(javascript|mailto):/i;

    function setBrowserUrlWithFallback(url, replace, state) {
      var oldUrl = $location.url();
      var oldState = $location.$$state;
      try {
        $browser.url(url, replace, state);

        // Make sure $location.state() returns referentially identical (not just deeply equal)
        // state object; this makes possible quick checking if the state changed in the digest
        // loop. Checking deep equality would be too expensive.
        $location.$$state = $browser.state();
      } catch (e) {
        // Restore old values if pushState fails
        $location.url(oldUrl);
        $location.$$state = oldState;

        throw e;
      }
    }

    $rootElement.on('click', function(event) {
      var rewriteLinks = html5Mode.rewriteLinks;
      // TODO(vojta): rewrite link when opening in new tab/window (in legacy browser)
      // currently we open nice url link and redirect then

      if (!rewriteLinks || event.ctrlKey || event.metaKey || event.shiftKey || event.which === 2 || event.button === 2) return;

      var elm = jqLite(event.target);

      // traverse the DOM up to find first A tag
      while (nodeName_(elm[0]) !== 'a') {
        // ignore rewriting if no A tag (reached root element, or no parent - removed from document)
        if (elm[0] === $rootElement[0] || !(elm = elm.parent())[0]) return;
      }

      if (isString(rewriteLinks) && isUndefined(elm.attr(rewriteLinks))) return;

      var absHref = elm.prop('href');
      // get the actual href attribute - see
      // http://msdn.microsoft.com/en-us/library/ie/dd347148(v=vs.85).aspx
      var relHref = elm.attr('href') || elm.attr('xlink:href');

      if (isObject(absHref) && absHref.toString() === '[object SVGAnimatedString]') {
        // SVGAnimatedString.animVal should be identical to SVGAnimatedString.baseVal, unless during
        // an animation.
        absHref = urlResolve(absHref.animVal).href;
      }

      // Ignore when url is started with javascript: or mailto:
      if (IGNORE_URI_REGEXP.test(absHref)) return;

      if (absHref && !elm.attr('target') && !event.isDefaultPrevented()) {
        if ($location.$$parseLinkUrl(absHref, relHref)) {
          // We do a preventDefault for all urls that are part of the angular application,
          // in html5mode and also without, so that we are able to abort navigation without
          // getting double entries in the location history.
          event.preventDefault();
          // update location manually
          if ($location.absUrl() !== $browser.url()) {
            $rootScope.$apply();
            // hack to work around FF6 bug 684208 when scenario runner clicks on links
            $window.angular['ff-684208-preventDefault'] = true;
          }
        }
      }
    });


    // rewrite hashbang url <> html5 url
    if (trimEmptyHash($location.absUrl()) !== trimEmptyHash(initialUrl)) {
      $browser.url($location.absUrl(), true);
    }

    var initializing = true;

    // update $location when $browser url changes
    $browser.onUrlChange(function(newUrl, newState) {

      if (!startsWith(newUrl, appBaseNoFile)) {
        // If we are navigating outside of the app then force a reload
        $window.location.href = newUrl;
        return;
      }

      $rootScope.$evalAsync(function() {
        var oldUrl = $location.absUrl();
        var oldState = $location.$$state;
        var defaultPrevented;
        newUrl = trimEmptyHash(newUrl);
        $location.$$parse(newUrl);
        $location.$$state = newState;

        defaultPrevented = $rootScope.$broadcast('$locationChangeStart', newUrl, oldUrl,
            newState, oldState).defaultPrevented;

        // if the location was changed by a `$locationChangeStart` handler then stop
        // processing this location change
        if ($location.absUrl() !== newUrl) return;

        if (defaultPrevented) {
          $location.$$parse(oldUrl);
          $location.$$state = oldState;
          setBrowserUrlWithFallback(oldUrl, false, oldState);
        } else {
          initializing = false;
          afterLocationChange(oldUrl, oldState);
        }
      });
      if (!$rootScope.$$phase) $rootScope.$digest();
    });

    // update browser
    $rootScope.$watch(function $locationWatch() {
      if (initializing || $location.$$urlUpdatedByLocation) {
        $location.$$urlUpdatedByLocation = false;

        var oldUrl = trimEmptyHash($browser.url());
        var newUrl = trimEmptyHash($location.absUrl());
        var oldState = $browser.state();
        var currentReplace = $location.$$replace;
        var urlOrStateChanged = oldUrl !== newUrl ||
          ($location.$$html5 && $sniffer.history && oldState !== $location.$$state);

        if (initializing || urlOrStateChanged) {
          initializing = false;

          $rootScope.$evalAsync(function() {
            var newUrl = $location.absUrl();
            var defaultPrevented = $rootScope.$broadcast('$locationChangeStart', newUrl, oldUrl,
                $location.$$state, oldState).defaultPrevented;

            // if the location was changed by a `$locationChangeStart` handler then stop
            // processing this location change
            if ($location.absUrl() !== newUrl) return;

            if (defaultPrevented) {
              $location.$$parse(oldUrl);
              $location.$$state = oldState;
            } else {
              if (urlOrStateChanged) {
                setBrowserUrlWithFallback(newUrl, currentReplace,
                                          oldState === $location.$$state ? null : $location.$$state);
              }
              afterLocationChange(oldUrl, oldState);
            }
          });
        }
      }

      $location.$$replace = false;

      // we don't need to return anything because $evalAsync will make the digest loop dirty when
      // there is a change
    });

    return $location;

    function afterLocationChange(oldUrl, oldState) {
      $rootScope.$broadcast('$locationChangeSuccess', $location.absUrl(), oldUrl,
        $location.$$state, oldState);
    }
}];
}

/**
 * @ngdoc service
 * @name $log
 * @requires $window
 *
 * @description
 * Simple service for logging. Default implementation safely writes the message
 * into the browser's console (if present).
 *
 * The main purpose of this service is to simplify debugging and troubleshooting.
 *
 * The default is to log `debug` messages. You can use
 * {@link ng.$logProvider ng.$logProvider#debugEnabled} to change this.
 *
 * @example
   <example module="logExample" name="log-service">
     <file name="script.js">
       angular.module('logExample', [])
         .controller('LogController', ['$scope', '$log', function($scope, $log) {
           $scope.$log = $log;
           $scope.message = 'Hello World!';
         }]);
     </file>
     <file name="index.html">
       <div ng-controller="LogController">
         <p>Reload this page with open console, enter text and hit the log button...</p>
         <label>Message:
         <input type="text" ng-model="message" /></label>
         <button ng-click="$log.log(message)">log</button>
         <button ng-click="$log.warn(message)">warn</button>
         <button ng-click="$log.info(message)">info</button>
         <button ng-click="$log.error(message)">error</button>
         <button ng-click="$log.debug(message)">debug</button>
       </div>
     </file>
   </example>
 */

/**
 * @ngdoc provider
 * @name $logProvider
 * @this
 *
 * @description
 * Use the `$logProvider` to configure how the application logs messages
 */
function $LogProvider() {
  var debug = true,
      self = this;

  /**
   * @ngdoc method
   * @name $logProvider#debugEnabled
   * @description
   * @param {boolean=} flag enable or disable debug level messages
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   */
  this.debugEnabled = function(flag) {
    if (isDefined(flag)) {
      debug = flag;
      return this;
    } else {
      return debug;
    }
  };

  this.$get = ['$window', function($window) {
    // Support: IE 9-11, Edge 12-14+
    // IE/Edge display errors in such a way that it requires the user to click in 4 places
    // to see the stack trace. There is no way to feature-detect it so there's a chance
    // of the user agent sniffing to go wrong but since it's only about logging, this shouldn't
    // break apps. Other browsers display errors in a sensible way and some of them map stack
    // traces along source maps if available so it makes sense to let browsers display it
    // as they want.
    var formatStackTrace = msie || /\bEdge\//.test($window.navigator && $window.navigator.userAgent);

    return {
      /**
       * @ngdoc method
       * @name $log#log
       *
       * @description
       * Write a log message
       */
      log: consoleLog('log'),

      /**
       * @ngdoc method
       * @name $log#info
       *
       * @description
       * Write an information message
       */
      info: consoleLog('info'),

      /**
       * @ngdoc method
       * @name $log#warn
       *
       * @description
       * Write a warning message
       */
      warn: consoleLog('warn'),

      /**
       * @ngdoc method
       * @name $log#error
       *
       * @description
       * Write an error message
       */
      error: consoleLog('error'),

      /**
       * @ngdoc method
       * @name $log#debug
       *
       * @description
       * Write a debug message
       */
      debug: (function() {
        var fn = consoleLog('debug');

        return function() {
          if (debug) {
            fn.apply(self, arguments);
          }
        };
      })()
    };

    function formatError(arg) {
      if (arg instanceof Error) {
        if (arg.stack && formatStackTrace) {
          arg = (arg.message && arg.stack.indexOf(arg.message) === -1)
              ? 'Error: ' + arg.message + '\n' + arg.stack
              : arg.stack;
        } else if (arg.sourceURL) {
          arg = arg.message + '\n' + arg.sourceURL + ':' + arg.line;
        }
      }
      return arg;
    }

    function consoleLog(type) {
      var console = $window.console || {},
          logFn = console[type] || console.log || noop,
          hasApply = false;

      // Note: reading logFn.apply throws an error in IE11 in IE8 document mode.
      // The reason behind this is that console.log has type "object" in IE8...
      try {
        hasApply = !!logFn.apply;
      } catch (e) { /* empty */ }

      if (hasApply) {
        return function() {
          var args = [];
          forEach(arguments, function(arg) {
            args.push(formatError(arg));
          });
          return logFn.apply(console, args);
        };
      }

      // we are IE which either doesn't have window.console => this is noop and we do nothing,
      // or we are IE where console.log doesn't have apply so we log at least first 2 args
      return function(arg1, arg2) {
        logFn(arg1, arg2 == null ? '' : arg2);
      };
    }
  }];
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

var $parseMinErr = minErr('$parse');

var objectValueOf = {}.constructor.prototype.valueOf;

// Sandboxing Angular Expressions
// ------------------------------
// Angular expressions are no longer sandboxed. So it is now even easier to access arbitrary JS code by
// various means such as obtaining a reference to native JS functions like the Function constructor.
//
// As an example, consider the following Angular expression:
//
//   {}.toString.constructor('alert("evil JS code")')
//
// It is important to realize that if you create an expression from a string that contains user provided
// content then it is possible that your application contains a security vulnerability to an XSS style attack.
//
// See https://docs.angularjs.org/guide/security


function getStringValue(name) {
  // Property names must be strings. This means that non-string objects cannot be used
  // as keys in an object. Any non-string object, including a number, is typecasted
  // into a string via the toString method.
  // -- MDN, https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Operators/Property_accessors#Property_names
  //
  // So, to ensure that we are checking the same `name` that JavaScript would use, we cast it
  // to a string. It's not always possible. If `name` is an object and its `toString` method is
  // 'broken' (doesn't return a string, isn't a function, etc.), an error will be thrown:
  //
  // TypeError: Cannot convert object to primitive value
  //
  // For performance reasons, we don't catch this error here and allow it to propagate up the call
  // stack. Note that you'll get the same error in JavaScript if you try to access a property using
  // such a 'broken' object as a key.
  return name + '';
}


var OPERATORS = createMap();
forEach('+ - * / % === !== == != < > <= >= && || ! = |'.split(' '), function(operator) { OPERATORS[operator] = true; });
var ESCAPE = {'n':'\n', 'f':'\f', 'r':'\r', 't':'\t', 'v':'\v', '\'':'\'', '"':'"'};


/////////////////////////////////////////


/**
 * @constructor
 */
var Lexer = function Lexer(options) {
  this.options = options;
};

Lexer.prototype = {
  constructor: Lexer,

  lex: function(text) {
    this.text = text;
    this.index = 0;
    this.tokens = [];

    while (this.index < this.text.length) {
      var ch = this.text.charAt(this.index);
      if (ch === '"' || ch === '\'') {
        this.readString(ch);
      } else if (this.isNumber(ch) || ch === '.' && this.isNumber(this.peek())) {
        this.readNumber();
      } else if (this.isIdentifierStart(this.peekMultichar())) {
        this.readIdent();
      } else if (this.is(ch, '(){}[].,;:?')) {
        this.tokens.push({index: this.index, text: ch});
        this.index++;
      } else if (this.isWhitespace(ch)) {
        this.index++;
      } else {
        var ch2 = ch + this.peek();
        var ch3 = ch2 + this.peek(2);
        var op1 = OPERATORS[ch];
        var op2 = OPERATORS[ch2];
        var op3 = OPERATORS[ch3];
        if (op1 || op2 || op3) {
          var token = op3 ? ch3 : (op2 ? ch2 : ch);
          this.tokens.push({index: this.index, text: token, operator: true});
          this.index += token.length;
        } else {
          this.throwError('Unexpected next character ', this.index, this.index + 1);
        }
      }
    }
    return this.tokens;
  },

  is: function(ch, chars) {
    return chars.indexOf(ch) !== -1;
  },

  peek: function(i) {
    var num = i || 1;
    return (this.index + num < this.text.length) ? this.text.charAt(this.index + num) : false;
  },

  isNumber: function(ch) {
    return ('0' <= ch && ch <= '9') && typeof ch === 'string';
  },

  isWhitespace: function(ch) {
    // IE treats non-breaking space as \u00A0
    return (ch === ' ' || ch === '\r' || ch === '\t' ||
            ch === '\n' || ch === '\v' || ch === '\u00A0');
  },

  isIdentifierStart: function(ch) {
    return this.options.isIdentifierStart ?
        this.options.isIdentifierStart(ch, this.codePointAt(ch)) :
        this.isValidIdentifierStart(ch);
  },

  isValidIdentifierStart: function(ch) {
    return ('a' <= ch && ch <= 'z' ||
            'A' <= ch && ch <= 'Z' ||
            '_' === ch || ch === '$');
  },

  isIdentifierContinue: function(ch) {
    return this.options.isIdentifierContinue ?
        this.options.isIdentifierContinue(ch, this.codePointAt(ch)) :
        this.isValidIdentifierContinue(ch);
  },

  isValidIdentifierContinue: function(ch, cp) {
    return this.isValidIdentifierStart(ch, cp) || this.isNumber(ch);
  },

  codePointAt: function(ch) {
    if (ch.length === 1) return ch.charCodeAt(0);
    // eslint-disable-next-line no-bitwise
    return (ch.charCodeAt(0) << 10) + ch.charCodeAt(1) - 0x35FDC00;
  },

  peekMultichar: function() {
    var ch = this.text.charAt(this.index);
    var peek = this.peek();
    if (!peek) {
      return ch;
    }
    var cp1 = ch.charCodeAt(0);
    var cp2 = peek.charCodeAt(0);
    if (cp1 >= 0xD800 && cp1 <= 0xDBFF && cp2 >= 0xDC00 && cp2 <= 0xDFFF) {
      return ch + peek;
    }
    return ch;
  },

  isExpOperator: function(ch) {
    return (ch === '-' || ch === '+' || this.isNumber(ch));
  },

  throwError: function(error, start, end) {
    end = end || this.index;
    var colStr = (isDefined(start)
            ? 's ' + start +  '-' + this.index + ' [' + this.text.substring(start, end) + ']'
            : ' ' + end);
    throw $parseMinErr('lexerr', 'Lexer Error: {0} at column{1} in expression [{2}].',
        error, colStr, this.text);
  },

  readNumber: function() {
    var number = '';
    var start = this.index;
    while (this.index < this.text.length) {
      var ch = lowercase(this.text.charAt(this.index));
      if (ch === '.' || this.isNumber(ch)) {
        number += ch;
      } else {
        var peekCh = this.peek();
        if (ch === 'e' && this.isExpOperator(peekCh)) {
          number += ch;
        } else if (this.isExpOperator(ch) &&
            peekCh && this.isNumber(peekCh) &&
            number.charAt(number.length - 1) === 'e') {
          number += ch;
        } else if (this.isExpOperator(ch) &&
            (!peekCh || !this.isNumber(peekCh)) &&
            number.charAt(number.length - 1) === 'e') {
          this.throwError('Invalid exponent');
        } else {
          break;
        }
      }
      this.index++;
    }
    this.tokens.push({
      index: start,
      text: number,
      constant: true,
      value: Number(number)
    });
  },

  readIdent: function() {
    var start = this.index;
    this.index += this.peekMultichar().length;
    while (this.index < this.text.length) {
      var ch = this.peekMultichar();
      if (!this.isIdentifierContinue(ch)) {
        break;
      }
      this.index += ch.length;
    }
    this.tokens.push({
      index: start,
      text: this.text.slice(start, this.index),
      identifier: true
    });
  },

  readString: function(quote) {
    var start = this.index;
    this.index++;
    var string = '';
    var rawString = quote;
    var escape = false;
    while (this.index < this.text.length) {
      var ch = this.text.charAt(this.index);
      rawString += ch;
      if (escape) {
        if (ch === 'u') {
          var hex = this.text.substring(this.index + 1, this.index + 5);
          if (!hex.match(/[\da-f]{4}/i)) {
            this.throwError('Invalid unicode escape [\\u' + hex + ']');
          }
          this.index += 4;
          string += String.fromCharCode(parseInt(hex, 16));
        } else {
          var rep = ESCAPE[ch];
          string = string + (rep || ch);
        }
        escape = false;
      } else if (ch === '\\') {
        escape = true;
      } else if (ch === quote) {
        this.index++;
        this.tokens.push({
          index: start,
          text: rawString,
          constant: true,
          value: string
        });
        return;
      } else {
        string += ch;
      }
      this.index++;
    }
    this.throwError('Unterminated quote', start);
  }
};

var AST = function AST(lexer, options) {
  this.lexer = lexer;
  this.options = options;
};

AST.Program = 'Program';
AST.ExpressionStatement = 'ExpressionStatement';
AST.AssignmentExpression = 'AssignmentExpression';
AST.ConditionalExpression = 'ConditionalExpression';
AST.LogicalExpression = 'LogicalExpression';
AST.BinaryExpression = 'BinaryExpression';
AST.UnaryExpression = 'UnaryExpression';
AST.CallExpression = 'CallExpression';
AST.MemberExpression = 'MemberExpression';
AST.Identifier = 'Identifier';
AST.Literal = 'Literal';
AST.ArrayExpression = 'ArrayExpression';
AST.Property = 'Property';
AST.ObjectExpression = 'ObjectExpression';
AST.ThisExpression = 'ThisExpression';
AST.LocalsExpression = 'LocalsExpression';

// Internal use only
AST.NGValueParameter = 'NGValueParameter';

AST.prototype = {
  ast: function(text) {
    this.text = text;
    this.tokens = this.lexer.lex(text);

    var value = this.program();

    if (this.tokens.length !== 0) {
      this.throwError('is an unexpected token', this.tokens[0]);
    }

    return value;
  },

  program: function() {
    var body = [];
    while (true) {
      if (this.tokens.length > 0 && !this.peek('}', ')', ';', ']'))
        body.push(this.expressionStatement());
      if (!this.expect(';')) {
        return { type: AST.Program, body: body};
      }
    }
  },

  expressionStatement: function() {
    return { type: AST.ExpressionStatement, expression: this.filterChain() };
  },

  filterChain: function() {
    var left = this.expression();
    while (this.expect('|')) {
      left = this.filter(left);
    }
    return left;
  },

  expression: function() {
    return this.assignment();
  },

  assignment: function() {
    var result = this.ternary();
    if (this.expect('=')) {
      if (!isAssignable(result)) {
        throw $parseMinErr('lval', 'Trying to assign a value to a non l-value');
      }

      result = { type: AST.AssignmentExpression, left: result, right: this.assignment(), operator: '='};
    }
    return result;
  },

  ternary: function() {
    var test = this.logicalOR();
    var alternate;
    var consequent;
    if (this.expect('?')) {
      alternate = this.expression();
      if (this.consume(':')) {
        consequent = this.expression();
        return { type: AST.ConditionalExpression, test: test, alternate: alternate, consequent: consequent};
      }
    }
    return test;
  },

  logicalOR: function() {
    var left = this.logicalAND();
    while (this.expect('||')) {
      left = { type: AST.LogicalExpression, operator: '||', left: left, right: this.logicalAND() };
    }
    return left;
  },

  logicalAND: function() {
    var left = this.equality();
    while (this.expect('&&')) {
      left = { type: AST.LogicalExpression, operator: '&&', left: left, right: this.equality()};
    }
    return left;
  },

  equality: function() {
    var left = this.relational();
    var token;
    while ((token = this.expect('==','!=','===','!=='))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.relational() };
    }
    return left;
  },

  relational: function() {
    var left = this.additive();
    var token;
    while ((token = this.expect('<', '>', '<=', '>='))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.additive() };
    }
    return left;
  },

  additive: function() {
    var left = this.multiplicative();
    var token;
    while ((token = this.expect('+','-'))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.multiplicative() };
    }
    return left;
  },

  multiplicative: function() {
    var left = this.unary();
    var token;
    while ((token = this.expect('*','/','%'))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.unary() };
    }
    return left;
  },

  unary: function() {
    var token;
    if ((token = this.expect('+', '-', '!'))) {
      return { type: AST.UnaryExpression, operator: token.text, prefix: true, argument: this.unary() };
    } else {
      return this.primary();
    }
  },

  primary: function() {
    var primary;
    if (this.expect('(')) {
      primary = this.filterChain();
      this.consume(')');
    } else if (this.expect('[')) {
      primary = this.arrayDeclaration();
    } else if (this.expect('{')) {
      primary = this.object();
    } else if (this.selfReferential.hasOwnProperty(this.peek().text)) {
      primary = copy(this.selfReferential[this.consume().text]);
    } else if (this.options.literals.hasOwnProperty(this.peek().text)) {
      primary = { type: AST.Literal, value: this.options.literals[this.consume().text]};
    } else if (this.peek().identifier) {
      primary = this.identifier();
    } else if (this.peek().constant) {
      primary = this.constant();
    } else {
      this.throwError('not a primary expression', this.peek());
    }

    var next;
    while ((next = this.expect('(', '[', '.'))) {
      if (next.text === '(') {
        primary = {type: AST.CallExpression, callee: primary, arguments: this.parseArguments() };
        this.consume(')');
      } else if (next.text === '[') {
        primary = { type: AST.MemberExpression, object: primary, property: this.expression(), computed: true };
        this.consume(']');
      } else if (next.text === '.') {
        primary = { type: AST.MemberExpression, object: primary, property: this.identifier(), computed: false };
      } else {
        this.throwError('IMPOSSIBLE');
      }
    }
    return primary;
  },

  filter: function(baseExpression) {
    var args = [baseExpression];
    var result = {type: AST.CallExpression, callee: this.identifier(), arguments: args, filter: true};

    while (this.expect(':')) {
      args.push(this.expression());
    }

    return result;
  },

  parseArguments: function() {
    var args = [];
    if (this.peekToken().text !== ')') {
      do {
        args.push(this.filterChain());
      } while (this.expect(','));
    }
    return args;
  },

  identifier: function() {
    var token = this.consume();
    if (!token.identifier) {
      this.throwError('is not a valid identifier', token);
    }
    return { type: AST.Identifier, name: token.text };
  },

  constant: function() {
    // TODO check that it is a constant
    return { type: AST.Literal, value: this.consume().value };
  },

  arrayDeclaration: function() {
    var elements = [];
    if (this.peekToken().text !== ']') {
      do {
        if (this.peek(']')) {
          // Support trailing commas per ES5.1.
          break;
        }
        elements.push(this.expression());
      } while (this.expect(','));
    }
    this.consume(']');

    return { type: AST.ArrayExpression, elements: elements };
  },

  object: function() {
    var properties = [], property;
    if (this.peekToken().text !== '}') {
      do {
        if (this.peek('}')) {
          // Support trailing commas per ES5.1.
          break;
        }
        property = {type: AST.Property, kind: 'init'};
        if (this.peek().constant) {
          property.key = this.constant();
          property.computed = false;
          this.consume(':');
          property.value = this.expression();
        } else if (this.peek().identifier) {
          property.key = this.identifier();
          property.computed = false;
          if (this.peek(':')) {
            this.consume(':');
            property.value = this.expression();
          } else {
            property.value = property.key;
          }
        } else if (this.peek('[')) {
          this.consume('[');
          property.key = this.expression();
          this.consume(']');
          property.computed = true;
          this.consume(':');
          property.value = this.expression();
        } else {
          this.throwError('invalid key', this.peek());
        }
        properties.push(property);
      } while (this.expect(','));
    }
    this.consume('}');

    return {type: AST.ObjectExpression, properties: properties };
  },

  throwError: function(msg, token) {
    throw $parseMinErr('syntax',
        'Syntax Error: Token \'{0}\' {1} at column {2} of the expression [{3}] starting at [{4}].',
          token.text, msg, (token.index + 1), this.text, this.text.substring(token.index));
  },

  consume: function(e1) {
    if (this.tokens.length === 0) {
      throw $parseMinErr('ueoe', 'Unexpected end of expression: {0}', this.text);
    }

    var token = this.expect(e1);
    if (!token) {
      this.throwError('is unexpected, expecting [' + e1 + ']', this.peek());
    }
    return token;
  },

  peekToken: function() {
    if (this.tokens.length === 0) {
      throw $parseMinErr('ueoe', 'Unexpected end of expression: {0}', this.text);
    }
    return this.tokens[0];
  },

  peek: function(e1, e2, e3, e4) {
    return this.peekAhead(0, e1, e2, e3, e4);
  },

  peekAhead: function(i, e1, e2, e3, e4) {
    if (this.tokens.length > i) {
      var token = this.tokens[i];
      var t = token.text;
      if (t === e1 || t === e2 || t === e3 || t === e4 ||
          (!e1 && !e2 && !e3 && !e4)) {
        return token;
      }
    }
    return false;
  },

  expect: function(e1, e2, e3, e4) {
    var token = this.peek(e1, e2, e3, e4);
    if (token) {
      this.tokens.shift();
      return token;
    }
    return false;
  },

  selfReferential: {
    'this': {type: AST.ThisExpression },
    '$locals': {type: AST.LocalsExpression }
  }
};

function ifDefined(v, d) {
  return typeof v !== 'undefined' ? v : d;
}

function plusFn(l, r) {
  if (typeof l === 'undefined') return r;
  if (typeof r === 'undefined') return l;
  return l + r;
}

function isStateless($filter, filterName) {
  var fn = $filter(filterName);
  return !fn.$stateful;
}

function findConstantAndWatchExpressions(ast, $filter) {
  var allConstants;
  var argsToWatch;
  var isStatelessFilter;
  switch (ast.type) {
  case AST.Program:
    allConstants = true;
    forEach(ast.body, function(expr) {
      findConstantAndWatchExpressions(expr.expression, $filter);
      allConstants = allConstants && expr.expression.constant;
    });
    ast.constant = allConstants;
    break;
  case AST.Literal:
    ast.constant = true;
    ast.toWatch = [];
    break;
  case AST.UnaryExpression:
    findConstantAndWatchExpressions(ast.argument, $filter);
    ast.constant = ast.argument.constant;
    ast.toWatch = ast.argument.toWatch;
    break;
  case AST.BinaryExpression:
    findConstantAndWatchExpressions(ast.left, $filter);
    findConstantAndWatchExpressions(ast.right, $filter);
    ast.constant = ast.left.constant && ast.right.constant;
    ast.toWatch = ast.left.toWatch.concat(ast.right.toWatch);
    break;
  case AST.LogicalExpression:
    findConstantAndWatchExpressions(ast.left, $filter);
    findConstantAndWatchExpressions(ast.right, $filter);
    ast.constant = ast.left.constant && ast.right.constant;
    ast.toWatch = ast.constant ? [] : [ast];
    break;
  case AST.ConditionalExpression:
    findConstantAndWatchExpressions(ast.test, $filter);
    findConstantAndWatchExpressions(ast.alternate, $filter);
    findConstantAndWatchExpressions(ast.consequent, $filter);
    ast.constant = ast.test.constant && ast.alternate.constant && ast.consequent.constant;
    ast.toWatch = ast.constant ? [] : [ast];
    break;
  case AST.Identifier:
    ast.constant = false;
    ast.toWatch = [ast];
    break;
  case AST.MemberExpression:
    findConstantAndWatchExpressions(ast.object, $filter);
    if (ast.computed) {
      findConstantAndWatchExpressions(ast.property, $filter);
    }
    ast.constant = ast.object.constant && (!ast.computed || ast.property.constant);
    ast.toWatch = [ast];
    break;
  case AST.CallExpression:
    isStatelessFilter = ast.filter ? isStateless($filter, ast.callee.name) : false;
    allConstants = isStatelessFilter;
    argsToWatch = [];
    forEach(ast.arguments, function(expr) {
      findConstantAndWatchExpressions(expr, $filter);
      allConstants = allConstants && expr.constant;
      if (!expr.constant) {
        argsToWatch.push.apply(argsToWatch, expr.toWatch);
      }
    });
    ast.constant = allConstants;
    ast.toWatch = isStatelessFilter ? argsToWatch : [ast];
    break;
  case AST.AssignmentExpression:
    findConstantAndWatchExpressions(ast.left, $filter);
    findConstantAndWatchExpressions(ast.right, $filter);
    ast.constant = ast.left.constant && ast.right.constant;
    ast.toWatch = [ast];
    break;
  case AST.ArrayExpression:
    allConstants = true;
    argsToWatch = [];
    forEach(ast.elements, function(expr) {
      findConstantAndWatchExpressions(expr, $filter);
      allConstants = allConstants && expr.constant;
      if (!expr.constant) {
        argsToWatch.push.apply(argsToWatch, expr.toWatch);
      }
    });
    ast.constant = allConstants;
    ast.toWatch = argsToWatch;
    break;
  case AST.ObjectExpression:
    allConstants = true;
    argsToWatch = [];
    forEach(ast.properties, function(property) {
      findConstantAndWatchExpressions(property.value, $filter);
      allConstants = allConstants && property.value.constant && !property.computed;
      if (!property.value.constant) {
        argsToWatch.push.apply(argsToWatch, property.value.toWatch);
      }
      if (property.computed) {
        findConstantAndWatchExpressions(property.key, $filter);
        if (!property.key.constant) {
          argsToWatch.push.apply(argsToWatch, property.key.toWatch);
        }
      }

    });
    ast.constant = allConstants;
    ast.toWatch = argsToWatch;
    break;
  case AST.ThisExpression:
    ast.constant = false;
    ast.toWatch = [];
    break;
  case AST.LocalsExpression:
    ast.constant = false;
    ast.toWatch = [];
    break;
  }
}

function getInputs(body) {
  if (body.length !== 1) return;
  var lastExpression = body[0].expression;
  var candidate = lastExpression.toWatch;
  if (candidate.length !== 1) return candidate;
  return candidate[0] !== lastExpression ? candidate : undefined;
}

function isAssignable(ast) {
  return ast.type === AST.Identifier || ast.type === AST.MemberExpression;
}

function assignableAST(ast) {
  if (ast.body.length === 1 && isAssignable(ast.body[0].expression)) {
    return {type: AST.AssignmentExpression, left: ast.body[0].expression, right: {type: AST.NGValueParameter}, operator: '='};
  }
}

function isLiteral(ast) {
  return ast.body.length === 0 ||
      ast.body.length === 1 && (
      ast.body[0].expression.type === AST.Literal ||
      ast.body[0].expression.type === AST.ArrayExpression ||
      ast.body[0].expression.type === AST.ObjectExpression);
}

function isConstant(ast) {
  return ast.constant;
}

function ASTCompiler(astBuilder, $filter) {
  this.astBuilder = astBuilder;
  this.$filter = $filter;
}

ASTCompiler.prototype = {
  compile: function(expression) {
    var self = this;
    var ast = this.astBuilder.ast(expression);
    this.state = {
      nextId: 0,
      filters: {},
      fn: {vars: [], body: [], own: {}},
      assign: {vars: [], body: [], own: {}},
      inputs: []
    };
    findConstantAndWatchExpressions(ast, self.$filter);
    var extra = '';
    var assignable;
    this.stage = 'assign';
    if ((assignable = assignableAST(ast))) {
      this.state.computing = 'assign';
      var result = this.nextId();
      this.recurse(assignable, result);
      this.return_(result);
      extra = 'fn.assign=' + this.generateFunction('assign', 's,v,l');
    }
    var toWatch = getInputs(ast.body);
    self.stage = 'inputs';
    forEach(toWatch, function(watch, key) {
      var fnKey = 'fn' + key;
      self.state[fnKey] = {vars: [], body: [], own: {}};
      self.state.computing = fnKey;
      var intoId = self.nextId();
      self.recurse(watch, intoId);
      self.return_(intoId);
      self.state.inputs.push(fnKey);
      watch.watchId = key;
    });
    this.state.computing = 'fn';
    this.stage = 'main';
    this.recurse(ast);
    var fnString =
      // The build and minification steps remove the string "use strict" from the code, but this is done using a regex.
      // This is a workaround for this until we do a better job at only removing the prefix only when we should.
      '"' + this.USE + ' ' + this.STRICT + '";\n' +
      this.filterPrefix() +
      'var fn=' + this.generateFunction('fn', 's,l,a,i') +
      extra +
      this.watchFns() +
      'return fn;';

    // eslint-disable-next-line no-new-func
    var fn = (new Function('$filter',
        'getStringValue',
        'ifDefined',
        'plus',
        fnString))(
          this.$filter,
          getStringValue,
          ifDefined,
          plusFn);
    this.state = this.stage = undefined;
    fn.literal = isLiteral(ast);
    fn.constant = isConstant(ast);
    return fn;
  },

  USE: 'use',

  STRICT: 'strict',

  watchFns: function() {
    var result = [];
    var fns = this.state.inputs;
    var self = this;
    forEach(fns, function(name) {
      result.push('var ' + name + '=' + self.generateFunction(name, 's'));
    });
    if (fns.length) {
      result.push('fn.inputs=[' + fns.join(',') + '];');
    }
    return result.join('');
  },

  generateFunction: function(name, params) {
    return 'function(' + params + '){' +
        this.varsPrefix(name) +
        this.body(name) +
        '};';
  },

  filterPrefix: function() {
    var parts = [];
    var self = this;
    forEach(this.state.filters, function(id, filter) {
      parts.push(id + '=$filter(' + self.escape(filter) + ')');
    });
    if (parts.length) return 'var ' + parts.join(',') + ';';
    return '';
  },

  varsPrefix: function(section) {
    return this.state[section].vars.length ? 'var ' + this.state[section].vars.join(',') + ';' : '';
  },

  body: function(section) {
    return this.state[section].body.join('');
  },

  recurse: function(ast, intoId, nameId, recursionFn, create, skipWatchIdCheck) {
    var left, right, self = this, args, expression, computed;
    recursionFn = recursionFn || noop;
    if (!skipWatchIdCheck && isDefined(ast.watchId)) {
      intoId = intoId || this.nextId();
      this.if_('i',
        this.lazyAssign(intoId, this.computedMember('i', ast.watchId)),
        this.lazyRecurse(ast, intoId, nameId, recursionFn, create, true)
      );
      return;
    }
    switch (ast.type) {
    case AST.Program:
      forEach(ast.body, function(expression, pos) {
        self.recurse(expression.expression, undefined, undefined, function(expr) { right = expr; });
        if (pos !== ast.body.length - 1) {
          self.current().body.push(right, ';');
        } else {
          self.return_(right);
        }
      });
      break;
    case AST.Literal:
      expression = this.escape(ast.value);
      this.assign(intoId, expression);
      recursionFn(intoId || expression);
      break;
    case AST.UnaryExpression:
      this.recurse(ast.argument, undefined, undefined, function(expr) { right = expr; });
      expression = ast.operator + '(' + this.ifDefined(right, 0) + ')';
      this.assign(intoId, expression);
      recursionFn(expression);
      break;
    case AST.BinaryExpression:
      this.recurse(ast.left, undefined, undefined, function(expr) { left = expr; });
      this.recurse(ast.right, undefined, undefined, function(expr) { right = expr; });
      if (ast.operator === '+') {
        expression = this.plus(left, right);
      } else if (ast.operator === '-') {
        expression = this.ifDefined(left, 0) + ast.operator + this.ifDefined(right, 0);
      } else {
        expression = '(' + left + ')' + ast.operator + '(' + right + ')';
      }
      this.assign(intoId, expression);
      recursionFn(expression);
      break;
    case AST.LogicalExpression:
      intoId = intoId || this.nextId();
      self.recurse(ast.left, intoId);
      self.if_(ast.operator === '&&' ? intoId : self.not(intoId), self.lazyRecurse(ast.right, intoId));
      recursionFn(intoId);
      break;
    case AST.ConditionalExpression:
      intoId = intoId || this.nextId();
      self.recurse(ast.test, intoId);
      self.if_(intoId, self.lazyRecurse(ast.alternate, intoId), self.lazyRecurse(ast.consequent, intoId));
      recursionFn(intoId);
      break;
    case AST.Identifier:
      intoId = intoId || this.nextId();
      if (nameId) {
        nameId.context = self.stage === 'inputs' ? 's' : this.assign(this.nextId(), this.getHasOwnProperty('l', ast.name) + '?l:s');
        nameId.computed = false;
        nameId.name = ast.name;
      }
      self.if_(self.stage === 'inputs' || self.not(self.getHasOwnProperty('l', ast.name)),
        function() {
          self.if_(self.stage === 'inputs' || 's', function() {
            if (create && create !== 1) {
              self.if_(
                self.isNull(self.nonComputedMember('s', ast.name)),
                self.lazyAssign(self.nonComputedMember('s', ast.name), '{}'));
            }
            self.assign(intoId, self.nonComputedMember('s', ast.name));
          });
        }, intoId && self.lazyAssign(intoId, self.nonComputedMember('l', ast.name))
        );
      recursionFn(intoId);
      break;
    case AST.MemberExpression:
      left = nameId && (nameId.context = this.nextId()) || this.nextId();
      intoId = intoId || this.nextId();
      self.recurse(ast.object, left, undefined, function() {
        self.if_(self.notNull(left), function() {
          if (ast.computed) {
            right = self.nextId();
            self.recurse(ast.property, right);
            self.getStringValue(right);
            if (create && create !== 1) {
              self.if_(self.not(self.computedMember(left, right)), self.lazyAssign(self.computedMember(left, right), '{}'));
            }
            expression = self.computedMember(left, right);
            self.assign(intoId, expression);
            if (nameId) {
              nameId.computed = true;
              nameId.name = right;
            }
          } else {
            if (create && create !== 1) {
              self.if_(self.isNull(self.nonComputedMember(left, ast.property.name)), self.lazyAssign(self.nonComputedMember(left, ast.property.name), '{}'));
            }
            expression = self.nonComputedMember(left, ast.property.name);
            self.assign(intoId, expression);
            if (nameId) {
              nameId.computed = false;
              nameId.name = ast.property.name;
            }
          }
        }, function() {
          self.assign(intoId, 'undefined');
        });
        recursionFn(intoId);
      }, !!create);
      break;
    case AST.CallExpression:
      intoId = intoId || this.nextId();
      if (ast.filter) {
        right = self.filter(ast.callee.name);
        args = [];
        forEach(ast.arguments, function(expr) {
          var argument = self.nextId();
          self.recurse(expr, argument);
          args.push(argument);
        });
        expression = right + '(' + args.join(',') + ')';
        self.assign(intoId, expression);
        recursionFn(intoId);
      } else {
        right = self.nextId();
        left = {};
        args = [];
        self.recurse(ast.callee, right, left, function() {
          self.if_(self.notNull(right), function() {
            forEach(ast.arguments, function(expr) {
              self.recurse(expr, ast.constant ? undefined : self.nextId(), undefined, function(argument) {
                args.push(argument);
              });
            });
            if (left.name) {
              expression = self.member(left.context, left.name, left.computed) + '(' + args.join(',') + ')';
            } else {
              expression = right + '(' + args.join(',') + ')';
            }
            self.assign(intoId, expression);
          }, function() {
            self.assign(intoId, 'undefined');
          });
          recursionFn(intoId);
        });
      }
      break;
    case AST.AssignmentExpression:
      right = this.nextId();
      left = {};
      this.recurse(ast.left, undefined, left, function() {
        self.if_(self.notNull(left.context), function() {
          self.recurse(ast.right, right);
          expression = self.member(left.context, left.name, left.computed) + ast.operator + right;
          self.assign(intoId, expression);
          recursionFn(intoId || expression);
        });
      }, 1);
      break;
    case AST.ArrayExpression:
      args = [];
      forEach(ast.elements, function(expr) {
        self.recurse(expr, ast.constant ? undefined : self.nextId(), undefined, function(argument) {
          args.push(argument);
        });
      });
      expression = '[' + args.join(',') + ']';
      this.assign(intoId, expression);
      recursionFn(intoId || expression);
      break;
    case AST.ObjectExpression:
      args = [];
      computed = false;
      forEach(ast.properties, function(property) {
        if (property.computed) {
          computed = true;
        }
      });
      if (computed) {
        intoId = intoId || this.nextId();
        this.assign(intoId, '{}');
        forEach(ast.properties, function(property) {
          if (property.computed) {
            left = self.nextId();
            self.recurse(property.key, left);
          } else {
            left = property.key.type === AST.Identifier ?
                       property.key.name :
                       ('' + property.key.value);
          }
          right = self.nextId();
          self.recurse(property.value, right);
          self.assign(self.member(intoId, left, property.computed), right);
        });
      } else {
        forEach(ast.properties, function(property) {
          self.recurse(property.value, ast.constant ? undefined : self.nextId(), undefined, function(expr) {
            args.push(self.escape(
                property.key.type === AST.Identifier ? property.key.name :
                  ('' + property.key.value)) +
                ':' + expr);
          });
        });
        expression = '{' + args.join(',') + '}';
        this.assign(intoId, expression);
      }
      recursionFn(intoId || expression);
      break;
    case AST.ThisExpression:
      this.assign(intoId, 's');
      recursionFn(intoId || 's');
      break;
    case AST.LocalsExpression:
      this.assign(intoId, 'l');
      recursionFn(intoId || 'l');
      break;
    case AST.NGValueParameter:
      this.assign(intoId, 'v');
      recursionFn(intoId || 'v');
      break;
    }
  },

  getHasOwnProperty: function(element, property) {
    var key = element + '.' + property;
    var own = this.current().own;
    if (!own.hasOwnProperty(key)) {
      own[key] = this.nextId(false, element + '&&(' + this.escape(property) + ' in ' + element + ')');
    }
    return own[key];
  },

  assign: function(id, value) {
    if (!id) return;
    this.current().body.push(id, '=', value, ';');
    return id;
  },

  filter: function(filterName) {
    if (!this.state.filters.hasOwnProperty(filterName)) {
      this.state.filters[filterName] = this.nextId(true);
    }
    return this.state.filters[filterName];
  },

  ifDefined: function(id, defaultValue) {
    return 'ifDefined(' + id + ',' + this.escape(defaultValue) + ')';
  },

  plus: function(left, right) {
    return 'plus(' + left + ',' + right + ')';
  },

  return_: function(id) {
    this.current().body.push('return ', id, ';');
  },

  if_: function(test, alternate, consequent) {
    if (test === true) {
      alternate();
    } else {
      var body = this.current().body;
      body.push('if(', test, '){');
      alternate();
      body.push('}');
      if (consequent) {
        body.push('else{');
        consequent();
        body.push('}');
      }
    }
  },

  not: function(expression) {
    return '!(' + expression + ')';
  },

  isNull: function(expression) {
    return expression + '==null';
  },

  notNull: function(expression) {
    return expression + '!=null';
  },

  nonComputedMember: function(left, right) {
    var SAFE_IDENTIFIER = /^[$_a-zA-Z][$_a-zA-Z0-9]*$/;
    var UNSAFE_CHARACTERS = /[^$_a-zA-Z0-9]/g;
    if (SAFE_IDENTIFIER.test(right)) {
      return left + '.' + right;
    } else {
      return left  + '["' + right.replace(UNSAFE_CHARACTERS, this.stringEscapeFn) + '"]';
    }
  },

  computedMember: function(left, right) {
    return left + '[' + right + ']';
  },

  member: function(left, right, computed) {
    if (computed) return this.computedMember(left, right);
    return this.nonComputedMember(left, right);
  },

  getStringValue: function(item) {
    this.assign(item, 'getStringValue(' + item + ')');
  },

  lazyRecurse: function(ast, intoId, nameId, recursionFn, create, skipWatchIdCheck) {
    var self = this;
    return function() {
      self.recurse(ast, intoId, nameId, recursionFn, create, skipWatchIdCheck);
    };
  },

  lazyAssign: function(id, value) {
    var self = this;
    return function() {
      self.assign(id, value);
    };
  },

  stringEscapeRegex: /[^ a-zA-Z0-9]/g,

  stringEscapeFn: function(c) {
    return '\\u' + ('0000' + c.charCodeAt(0).toString(16)).slice(-4);
  },

  escape: function(value) {
    if (isString(value)) return '\'' + value.replace(this.stringEscapeRegex, this.stringEscapeFn) + '\'';
    if (isNumber(value)) return value.toString();
    if (value === true) return 'true';
    if (value === false) return 'false';
    if (value === null) return 'null';
    if (typeof value === 'undefined') return 'undefined';

    throw $parseMinErr('esc', 'IMPOSSIBLE');
  },

  nextId: function(skip, init) {
    var id = 'v' + (this.state.nextId++);
    if (!skip) {
      this.current().vars.push(id + (init ? '=' + init : ''));
    }
    return id;
  },

  current: function() {
    return this.state[this.state.computing];
  }
};


function ASTInterpreter(astBuilder, $filter) {
  this.astBuilder = astBuilder;
  this.$filter = $filter;
}

ASTInterpreter.prototype = {
  compile: function(expression) {
    var self = this;
    var ast = this.astBuilder.ast(expression);
    findConstantAndWatchExpressions(ast, self.$filter);
    var assignable;
    var assign;
    if ((assignable = assignableAST(ast))) {
      assign = this.recurse(assignable);
    }
    var toWatch = getInputs(ast.body);
    var inputs;
    if (toWatch) {
      inputs = [];
      forEach(toWatch, function(watch, key) {
        var input = self.recurse(watch);
        watch.input = input;
        inputs.push(input);
        watch.watchId = key;
      });
    }
    var expressions = [];
    forEach(ast.body, function(expression) {
      expressions.push(self.recurse(expression.expression));
    });
    var fn = ast.body.length === 0 ? noop :
             ast.body.length === 1 ? expressions[0] :
             function(scope, locals) {
               var lastValue;
               forEach(expressions, function(exp) {
                 lastValue = exp(scope, locals);
               });
               return lastValue;
             };
    if (assign) {
      fn.assign = function(scope, value, locals) {
        return assign(scope, locals, value);
      };
    }
    if (inputs) {
      fn.inputs = inputs;
    }
    fn.literal = isLiteral(ast);
    fn.constant = isConstant(ast);
    return fn;
  },

  recurse: function(ast, context, create) {
    var left, right, self = this, args;
    if (ast.input) {
      return this.inputs(ast.input, ast.watchId);
    }
    switch (ast.type) {
    case AST.Literal:
      return this.value(ast.value, context);
    case AST.UnaryExpression:
      right = this.recurse(ast.argument);
      return this['unary' + ast.operator](right, context);
    case AST.BinaryExpression:
      left = this.recurse(ast.left);
      right = this.recurse(ast.right);
      return this['binary' + ast.operator](left, right, context);
    case AST.LogicalExpression:
      left = this.recurse(ast.left);
      right = this.recurse(ast.right);
      return this['binary' + ast.operator](left, right, context);
    case AST.ConditionalExpression:
      return this['ternary?:'](
        this.recurse(ast.test),
        this.recurse(ast.alternate),
        this.recurse(ast.consequent),
        context
      );
    case AST.Identifier:
      return self.identifier(ast.name, context, create);
    case AST.MemberExpression:
      left = this.recurse(ast.object, false, !!create);
      if (!ast.computed) {
        right = ast.property.name;
      }
      if (ast.computed) right = this.recurse(ast.property);
      return ast.computed ?
        this.computedMember(left, right, context, create) :
        this.nonComputedMember(left, right, context, create);
    case AST.CallExpression:
      args = [];
      forEach(ast.arguments, function(expr) {
        args.push(self.recurse(expr));
      });
      if (ast.filter) right = this.$filter(ast.callee.name);
      if (!ast.filter) right = this.recurse(ast.callee, true);
      return ast.filter ?
        function(scope, locals, assign, inputs) {
          var values = [];
          for (var i = 0; i < args.length; ++i) {
            values.push(args[i](scope, locals, assign, inputs));
          }
          var value = right.apply(undefined, values, inputs);
          return context ? {context: undefined, name: undefined, value: value} : value;
        } :
        function(scope, locals, assign, inputs) {
          var rhs = right(scope, locals, assign, inputs);
          var value;
          if (rhs.value != null) {
            var values = [];
            for (var i = 0; i < args.length; ++i) {
              values.push(args[i](scope, locals, assign, inputs));
            }
            value = rhs.value.apply(rhs.context, values);
          }
          return context ? {value: value} : value;
        };
    case AST.AssignmentExpression:
      left = this.recurse(ast.left, true, 1);
      right = this.recurse(ast.right);
      return function(scope, locals, assign, inputs) {
        var lhs = left(scope, locals, assign, inputs);
        var rhs = right(scope, locals, assign, inputs);
        lhs.context[lhs.name] = rhs;
        return context ? {value: rhs} : rhs;
      };
    case AST.ArrayExpression:
      args = [];
      forEach(ast.elements, function(expr) {
        args.push(self.recurse(expr));
      });
      return function(scope, locals, assign, inputs) {
        var value = [];
        for (var i = 0; i < args.length; ++i) {
          value.push(args[i](scope, locals, assign, inputs));
        }
        return context ? {value: value} : value;
      };
    case AST.ObjectExpression:
      args = [];
      forEach(ast.properties, function(property) {
        if (property.computed) {
          args.push({key: self.recurse(property.key),
                     computed: true,
                     value: self.recurse(property.value)
          });
        } else {
          args.push({key: property.key.type === AST.Identifier ?
                          property.key.name :
                          ('' + property.key.value),
                     computed: false,
                     value: self.recurse(property.value)
          });
        }
      });
      return function(scope, locals, assign, inputs) {
        var value = {};
        for (var i = 0; i < args.length; ++i) {
          if (args[i].computed) {
            value[args[i].key(scope, locals, assign, inputs)] = args[i].value(scope, locals, assign, inputs);
          } else {
            value[args[i].key] = args[i].value(scope, locals, assign, inputs);
          }
        }
        return context ? {value: value} : value;
      };
    case AST.ThisExpression:
      return function(scope) {
        return context ? {value: scope} : scope;
      };
    case AST.LocalsExpression:
      return function(scope, locals) {
        return context ? {value: locals} : locals;
      };
    case AST.NGValueParameter:
      return function(scope, locals, assign) {
        return context ? {value: assign} : assign;
      };
    }
  },

  'unary+': function(argument, context) {
    return function(scope, locals, assign, inputs) {
      var arg = argument(scope, locals, assign, inputs);
      if (isDefined(arg)) {
        arg = +arg;
      } else {
        arg = 0;
      }
      return context ? {value: arg} : arg;
    };
  },
  'unary-': function(argument, context) {
    return function(scope, locals, assign, inputs) {
      var arg = argument(scope, locals, assign, inputs);
      if (isDefined(arg)) {
        arg = -arg;
      } else {
        arg = -0;
      }
      return context ? {value: arg} : arg;
    };
  },
  'unary!': function(argument, context) {
    return function(scope, locals, assign, inputs) {
      var arg = !argument(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary+': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      var rhs = right(scope, locals, assign, inputs);
      var arg = plusFn(lhs, rhs);
      return context ? {value: arg} : arg;
    };
  },
  'binary-': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      var rhs = right(scope, locals, assign, inputs);
      var arg = (isDefined(lhs) ? lhs : 0) - (isDefined(rhs) ? rhs : 0);
      return context ? {value: arg} : arg;
    };
  },
  'binary*': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) * right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary/': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) / right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary%': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) % right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary===': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) === right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary!==': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) !== right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary==': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      // eslint-disable-next-line eqeqeq
      var arg = left(scope, locals, assign, inputs) == right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary!=': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      // eslint-disable-next-line eqeqeq
      var arg = left(scope, locals, assign, inputs) != right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary<': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) < right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary>': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) > right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary<=': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) <= right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary>=': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) >= right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary&&': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) && right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary||': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) || right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'ternary?:': function(test, alternate, consequent, context) {
    return function(scope, locals, assign, inputs) {
      var arg = test(scope, locals, assign, inputs) ? alternate(scope, locals, assign, inputs) : consequent(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  value: function(value, context) {
    return function() { return context ? {context: undefined, name: undefined, value: value} : value; };
  },
  identifier: function(name, context, create) {
    return function(scope, locals, assign, inputs) {
      var base = locals && (name in locals) ? locals : scope;
      if (create && create !== 1 && base && base[name] == null) {
        base[name] = {};
      }
      var value = base ? base[name] : undefined;
      if (context) {
        return {context: base, name: name, value: value};
      } else {
        return value;
      }
    };
  },
  computedMember: function(left, right, context, create) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      var rhs;
      var value;
      if (lhs != null) {
        rhs = right(scope, locals, assign, inputs);
        rhs = getStringValue(rhs);
        if (create && create !== 1) {
          if (lhs && !(lhs[rhs])) {
            lhs[rhs] = {};
          }
        }
        value = lhs[rhs];
      }
      if (context) {
        return {context: lhs, name: rhs, value: value};
      } else {
        return value;
      }
    };
  },
  nonComputedMember: function(left, right, context, create) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      if (create && create !== 1) {
        if (lhs && lhs[right] == null) {
          lhs[right] = {};
        }
      }
      var value = lhs != null ? lhs[right] : undefined;
      if (context) {
        return {context: lhs, name: right, value: value};
      } else {
        return value;
      }
    };
  },
  inputs: function(input, watchId) {
    return function(scope, value, locals, inputs) {
      if (inputs) return inputs[watchId];
      return input(scope, value, locals);
    };
  }
};

/**
 * @constructor
 */
var Parser = function Parser(lexer, $filter, options) {
  this.lexer = lexer;
  this.$filter = $filter;
  this.options = options;
  this.ast = new AST(lexer, options);
  this.astCompiler = options.csp ? new ASTInterpreter(this.ast, $filter) :
                                   new ASTCompiler(this.ast, $filter);
};

Parser.prototype = {
  constructor: Parser,

  parse: function(text) {
    return this.astCompiler.compile(text);
  }
};

function getValueOf(value) {
  return isFunction(value.valueOf) ? value.valueOf() : objectValueOf.call(value);
}

///////////////////////////////////

/**
 * @ngdoc service
 * @name $parse
 * @kind function
 *
 * @description
 *
 * Converts Angular {@link guide/expression expression} into a function.
 *
 * ```js
 *   var getter = $parse('user.name');
 *   var setter = getter.assign;
 *   var context = {user:{name:'angular'}};
 *   var locals = {user:{name:'local'}};
 *
 *   expect(getter(context)).toEqual('angular');
 *   setter(context, 'newValue');
 *   expect(context.user.name).toEqual('newValue');
 *   expect(getter(context, locals)).toEqual('local');
 * ```
 *
 *
 * @param {string} expression String expression to compile.
 * @returns {function(context, locals)} a function which represents the compiled expression:
 *
 *    * `context` – `{object}` – an object against which any expressions embedded in the strings
 *      are evaluated against (typically a scope object).
 *    * `locals` – `{object=}` – local variables context object, useful for overriding values in
 *      `context`.
 *
 *    The returned function also has the following properties:
 *      * `literal` – `{boolean}` – whether the expression's top-level node is a JavaScript
 *        literal.
 *      * `constant` – `{boolean}` – whether the expression is made entirely of JavaScript
 *        constant literals.
 *      * `assign` – `{?function(context, value)}` – if the expression is assignable, this will be
 *        set to a function to change its value on the given context.
 *
 */


/**
 * @ngdoc provider
 * @name $parseProvider
 * @this
 *
 * @description
 * `$parseProvider` can be used for configuring the default behavior of the {@link ng.$parse $parse}
 *  service.
 */
function $ParseProvider() {
  var cache = createMap();
  var literals = {
    'true': true,
    'false': false,
    'null': null,
    'undefined': undefined
  };
  var identStart, identContinue;

  /**
   * @ngdoc method
   * @name $parseProvider#addLiteral
   * @description
   *
   * Configure $parse service to add literal values that will be present as literal at expressions.
   *
   * @param {string} literalName Token for the literal value. The literal name value must be a valid literal name.
   * @param {*} literalValue Value for this literal. All literal values must be primitives or `undefined`.
   *
   **/
  this.addLiteral = function(literalName, literalValue) {
    literals[literalName] = literalValue;
  };

 /**
  * @ngdoc method
  * @name $parseProvider#setIdentifierFns
  *
  * @description
  *
  * Allows defining the set of characters that are allowed in Angular expressions. The function
  * `identifierStart` will get called to know if a given character is a valid character to be the
  * first character for an identifier. The function `identifierContinue` will get called to know if
  * a given character is a valid character to be a follow-up identifier character. The functions
  * `identifierStart` and `identifierContinue` will receive as arguments the single character to be
  * identifier and the character code point. These arguments will be `string` and `numeric`. Keep in
  * mind that the `string` parameter can be two characters long depending on the character
  * representation. It is expected for the function to return `true` or `false`, whether that
  * character is allowed or not.
  *
  * Since this function will be called extensively, keep the implementation of these functions fast,
  * as the performance of these functions have a direct impact on the expressions parsing speed.
  *
  * @param {function=} identifierStart The function that will decide whether the given character is
  *   a valid identifier start character.
  * @param {function=} identifierContinue The function that will decide whether the given character is
  *   a valid identifier continue character.
  */
  this.setIdentifierFns = function(identifierStart, identifierContinue) {
    identStart = identifierStart;
    identContinue = identifierContinue;
    return this;
  };

  this.$get = ['$filter', function($filter) {
    var noUnsafeEval = csp().noUnsafeEval;
    var $parseOptions = {
          csp: noUnsafeEval,
          literals: copy(literals),
          isIdentifierStart: isFunction(identStart) && identStart,
          isIdentifierContinue: isFunction(identContinue) && identContinue
        };
    return $parse;

    function $parse(exp, interceptorFn) {
      var parsedExpression, oneTime, cacheKey;

      switch (typeof exp) {
        case 'string':
          exp = exp.trim();
          cacheKey = exp;

          parsedExpression = cache[cacheKey];

          if (!parsedExpression) {
            if (exp.charAt(0) === ':' && exp.charAt(1) === ':') {
              oneTime = true;
              exp = exp.substring(2);
            }
            var lexer = new Lexer($parseOptions);
            var parser = new Parser(lexer, $filter, $parseOptions);
            parsedExpression = parser.parse(exp);
            if (parsedExpression.constant) {
              parsedExpression.$$watchDelegate = constantWatchDelegate;
            } else if (oneTime) {
              parsedExpression.$$watchDelegate = parsedExpression.literal ?
                  oneTimeLiteralWatchDelegate : oneTimeWatchDelegate;
            } else if (parsedExpression.inputs) {
              parsedExpression.$$watchDelegate = inputsWatchDelegate;
            }
            cache[cacheKey] = parsedExpression;
          }
          return addInterceptor(parsedExpression, interceptorFn);

        case 'function':
          return addInterceptor(exp, interceptorFn);

        default:
          return addInterceptor(noop, interceptorFn);
      }
    }

    function expressionInputDirtyCheck(newValue, oldValueOfValue, compareObjectIdentity) {

      if (newValue == null || oldValueOfValue == null) { // null/undefined
        return newValue === oldValueOfValue;
      }

      if (typeof newValue === 'object' && !compareObjectIdentity) {

        // attempt to convert the value to a primitive type
        // TODO(docs): add a note to docs that by implementing valueOf even objects and arrays can
        //             be cheaply dirty-checked
        newValue = getValueOf(newValue);

        if (typeof newValue === 'object') {
          // objects/arrays are not supported - deep-watching them would be too expensive
          return false;
        }

        // fall-through to the primitive equality check
      }

      //Primitive or NaN
      // eslint-disable-next-line no-self-compare
      return newValue === oldValueOfValue || (newValue !== newValue && oldValueOfValue !== oldValueOfValue);
    }

    function inputsWatchDelegate(scope, listener, objectEquality, parsedExpression, prettyPrintExpression) {
      var inputExpressions = parsedExpression.inputs;
      var lastResult;

      if (inputExpressions.length === 1) {
        var oldInputValueOf = expressionInputDirtyCheck; // init to something unique so that equals check fails
        inputExpressions = inputExpressions[0];
        return scope.$watch(function expressionInputWatch(scope) {
          var newInputValue = inputExpressions(scope);
          if (!expressionInputDirtyCheck(newInputValue, oldInputValueOf, parsedExpression.literal)) {
            lastResult = parsedExpression(scope, undefined, undefined, [newInputValue]);
            oldInputValueOf = newInputValue && getValueOf(newInputValue);
          }
          return lastResult;
        }, listener, objectEquality, prettyPrintExpression);
      }

      var oldInputValueOfValues = [];
      var oldInputValues = [];
      for (var i = 0, ii = inputExpressions.length; i < ii; i++) {
        oldInputValueOfValues[i] = expressionInputDirtyCheck; // init to something unique so that equals check fails
        oldInputValues[i] = null;
      }

      return scope.$watch(function expressionInputsWatch(scope) {
        var changed = false;

        for (var i = 0, ii = inputExpressions.length; i < ii; i++) {
          var newInputValue = inputExpressions[i](scope);
          if (changed || (changed = !expressionInputDirtyCheck(newInputValue, oldInputValueOfValues[i], parsedExpression.literal))) {
            oldInputValues[i] = newInputValue;
            oldInputValueOfValues[i] = newInputValue && getValueOf(newInputValue);
          }
        }

        if (changed) {
          lastResult = parsedExpression(scope, undefined, undefined, oldInputValues);
        }

        return lastResult;
      }, listener, objectEquality, prettyPrintExpression);
    }

    function oneTimeWatchDelegate(scope, listener, objectEquality, parsedExpression, prettyPrintExpression) {
      var unwatch, lastValue;
      if (parsedExpression.inputs) {
        unwatch = inputsWatchDelegate(scope, oneTimeListener, objectEquality, parsedExpression, prettyPrintExpression);
      } else {
        unwatch = scope.$watch(oneTimeWatch, oneTimeListener, objectEquality);
      }
      return unwatch;

      function oneTimeWatch(scope) {
        return parsedExpression(scope);
      }
      function oneTimeListener(value, old, scope) {
        lastValue = value;
        if (isFunction(listener)) {
          listener(value, old, scope);
        }
        if (isDefined(value)) {
          scope.$$postDigest(function() {
            if (isDefined(lastValue)) {
              unwatch();
            }
          });
        }
      }
    }

    function oneTimeLiteralWatchDelegate(scope, listener, objectEquality, parsedExpression) {
      var unwatch, lastValue;
      unwatch = scope.$watch(function oneTimeWatch(scope) {
        return parsedExpression(scope);
      }, function oneTimeListener(value, old, scope) {
        lastValue = value;
        if (isFunction(listener)) {
          listener(value, old, scope);
        }
        if (isAllDefined(value)) {
          scope.$$postDigest(function() {
            if (isAllDefined(lastValue)) unwatch();
          });
        }
      }, objectEquality);

      return unwatch;

      function isAllDefined(value) {
        var allDefined = true;
        forEach(value, function(val) {
          if (!isDefined(val)) allDefined = false;
        });
        return allDefined;
      }
    }

    function constantWatchDelegate(scope, listener, objectEquality, parsedExpression) {
      var unwatch = scope.$watch(function constantWatch(scope) {
        unwatch();
        return parsedExpression(scope);
      }, listener, objectEquality);
      return unwatch;
    }

    function addInterceptor(parsedExpression, interceptorFn) {
      if (!interceptorFn) return parsedExpression;
      var watchDelegate = parsedExpression.$$watchDelegate;
      var useInputs = false;

      var regularWatch =
          watchDelegate !== oneTimeLiteralWatchDelegate &&
          watchDelegate !== oneTimeWatchDelegate;

      var fn = regularWatch ? function regularInterceptedExpression(scope, locals, assign, inputs) {
        var value = useInputs && inputs ? inputs[0] : parsedExpression(scope, locals, assign, inputs);
        return interceptorFn(value, scope, locals);
      } : function oneTimeInterceptedExpression(scope, locals, assign, inputs) {
        var value = parsedExpression(scope, locals, assign, inputs);
        var result = interceptorFn(value, scope, locals);
        // we only return the interceptor's result if the
        // initial value is defined (for bind-once)
        return isDefined(value) ? result : value;
      };

      // Propagate $$watchDelegates other then inputsWatchDelegate
      useInputs = !parsedExpression.inputs;
      if (parsedExpression.$$watchDelegate &&
          parsedExpression.$$watchDelegate !== inputsWatchDelegate) {
        fn.$$watchDelegate = parsedExpression.$$watchDelegate;
        fn.inputs = parsedExpression.inputs;
      } else if (!interceptorFn.$stateful) {
        // If there is an interceptor, but no watchDelegate then treat the interceptor like
        // we treat filters - it is assumed to be a pure function unless flagged with $stateful
        fn.$$watchDelegate = inputsWatchDelegate;
        fn.inputs = parsedExpression.inputs ? parsedExpression.inputs : [parsedExpression];
      }

      return fn;
    }
  }];
}

/**
 * @ngdoc service
 * @name $q
 * @requires $rootScope
 *
 * @description
 * A service that helps you run functions asynchronously, and use their return values (or exceptions)
 * when they are done processing.
 *
 * This is a [Promises/A+](https://promisesaplus.com/)-compliant implementation of promises/deferred
 * objects inspired by [Kris Kowal's Q](https://github.com/kriskowal/q).
 *
 * $q can be used in two fashions --- one which is more similar to Kris Kowal's Q or jQuery's Deferred
 * implementations, and the other which resembles ES6 (ES2015) promises to some degree.
 *
 * # $q constructor
 *
 * The streamlined ES6 style promise is essentially just using $q as a constructor which takes a `resolver`
 * function as the first argument. This is similar to the native Promise implementation from ES6,
 * see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise).
 *
 * While the constructor-style use is supported, not all of the supporting methods from ES6 promises are
 * available yet.
 *
 * It can be used like so:
 *
 * ```js
 *   // for the purpose of this example let's assume that variables `$q` and `okToGreet`
 *   // are available in the current lexical scope (they could have been injected or passed in).
 *
 *   function asyncGreet(name) {
 *     // perform some asynchronous operation, resolve or reject the promise when appropriate.
 *     return $q(function(resolve, reject) {
 *       setTimeout(function() {
 *         if (okToGreet(name)) {
 *           resolve('Hello, ' + name + '!');
 *         } else {
 *           reject('Greeting ' + name + ' is not allowed.');
 *         }
 *       }, 1000);
 *     });
 *   }
 *
 *   var promise = asyncGreet('Robin Hood');
 *   promise.then(function(greeting) {
 *     alert('Success: ' + greeting);
 *   }, function(reason) {
 *     alert('Failed: ' + reason);
 *   });
 * ```
 *
 * Note: progress/notify callbacks are not currently supported via the ES6-style interface.
 *
 * Note: unlike ES6 behavior, an exception thrown in the constructor function will NOT implicitly reject the promise.
 *
 * However, the more traditional CommonJS-style usage is still available, and documented below.
 *
 * [The CommonJS Promise proposal](http://wiki.commonjs.org/wiki/Promises) describes a promise as an
 * interface for interacting with an object that represents the result of an action that is
 * performed asynchronously, and may or may not be finished at any given point in time.
 *
 * From the perspective of dealing with error handling, deferred and promise APIs are to
 * asynchronous programming what `try`, `catch` and `throw` keywords are to synchronous programming.
 *
 * ```js
 *   // for the purpose of this example let's assume that variables `$q` and `okToGreet`
 *   // are available in the current lexical scope (they could have been injected or passed in).
 *
 *   function asyncGreet(name) {
 *     var deferred = $q.defer();
 *
 *     setTimeout(function() {
 *       deferred.notify('About to greet ' + name + '.');
 *
 *       if (okToGreet(name)) {
 *         deferred.resolve('Hello, ' + name + '!');
 *       } else {
 *         deferred.reject('Greeting ' + name + ' is not allowed.');
 *       }
 *     }, 1000);
 *
 *     return deferred.promise;
 *   }
 *
 *   var promise = asyncGreet('Robin Hood');
 *   promise.then(function(greeting) {
 *     alert('Success: ' + greeting);
 *   }, function(reason) {
 *     alert('Failed: ' + reason);
 *   }, function(update) {
 *     alert('Got notification: ' + update);
 *   });
 * ```
 *
 * At first it might not be obvious why this extra complexity is worth the trouble. The payoff
 * comes in the way of guarantees that promise and deferred APIs make, see
 * https://github.com/kriskowal/uncommonjs/blob/master/promises/specification.md.
 *
 * Additionally the promise api allows for composition that is very hard to do with the
 * traditional callback ([CPS](http://en.wikipedia.org/wiki/Continuation-passing_style)) approach.
 * For more on this please see the [Q documentation](https://github.com/kriskowal/q) especially the
 * section on serial or parallel joining of promises.
 *
 * # The Deferred API
 *
 * A new instance of deferred is constructed by calling `$q.defer()`.
 *
 * The purpose of the deferred object is to expose the associated Promise instance as well as APIs
 * that can be used for signaling the successful or unsuccessful completion, as well as the status
 * of the task.
 *
 * **Methods**
 *
 * - `resolve(value)` – resolves the derived promise with the `value`. If the value is a rejection
 *   constructed via `$q.reject`, the promise will be rejected instead.
 * - `reject(reason)` – rejects the derived promise with the `reason`. This is equivalent to
 *   resolving it with a rejection constructed via `$q.reject`.
 * - `notify(value)` - provides updates on the status of the promise's execution. This may be called
 *   multiple times before the promise is either resolved or rejected.
 *
 * **Properties**
 *
 * - promise – `{Promise}` – promise object associated with this deferred.
 *
 *
 * # The Promise API
 *
 * A new promise instance is created when a deferred instance is created and can be retrieved by
 * calling `deferred.promise`.
 *
 * The purpose of the promise object is to allow for interested parties to get access to the result
 * of the deferred task when it completes.
 *
 * **Methods**
 *
 * - `then(successCallback, [errorCallback], [notifyCallback])` – regardless of when the promise was or
 *   will be resolved or rejected, `then` calls one of the success or error callbacks asynchronously
 *   as soon as the result is available. The callbacks are called with a single argument: the result
 *   or rejection reason. Additionally, the notify callback may be called zero or more times to
 *   provide a progress indication, before the promise is resolved or rejected.
 *
 *   This method *returns a new promise* which is resolved or rejected via the return value of the
 *   `successCallback`, `errorCallback` (unless that value is a promise, in which case it is resolved
 *   with the value which is resolved in that promise using
 *   [promise chaining](http://www.html5rocks.com/en/tutorials/es6/promises/#toc-promises-queues)).
 *   It also notifies via the return value of the `notifyCallback` method. The promise cannot be
 *   resolved or rejected from the notifyCallback method. The errorCallback and notifyCallback
 *   arguments are optional.
 *
 * - `catch(errorCallback)` – shorthand for `promise.then(null, errorCallback)`
 *
 * - `finally(callback, notifyCallback)` – allows you to observe either the fulfillment or rejection of a promise,
 *   but to do so without modifying the final value. This is useful to release resources or do some
 *   clean-up that needs to be done whether the promise was rejected or resolved. See the [full
 *   specification](https://github.com/kriskowal/q/wiki/API-Reference#promisefinallycallback) for
 *   more information.
 *
 * # Chaining promises
 *
 * Because calling the `then` method of a promise returns a new derived promise, it is easily
 * possible to create a chain of promises:
 *
 * ```js
 *   promiseB = promiseA.then(function(result) {
 *     return result + 1;
 *   });
 *
 *   // promiseB will be resolved immediately after promiseA is resolved and its value
 *   // will be the result of promiseA incremented by 1
 * ```
 *
 * It is possible to create chains of any length and since a promise can be resolved with another
 * promise (which will defer its resolution further), it is possible to pause/defer resolution of
 * the promises at any point in the chain. This makes it possible to implement powerful APIs like
 * $http's response interceptors.
 *
 *
 * # Differences between Kris Kowal's Q and $q
 *
 *  There are two main differences:
 *
 * - $q is integrated with the {@link ng.$rootScope.Scope} Scope model observation
 *   mechanism in angular, which means faster propagation of resolution or rejection into your
 *   models and avoiding unnecessary browser repaints, which would result in flickering UI.
 * - Q has many more features than $q, but that comes at a cost of bytes. $q is tiny, but contains
 *   all the important functionality needed for common async tasks.
 *
 * # Testing
 *
 *  ```js
 *    it('should simulate promise', inject(function($q, $rootScope) {
 *      var deferred = $q.defer();
 *      var promise = deferred.promise;
 *      var resolvedValue;
 *
 *      promise.then(function(value) { resolvedValue = value; });
 *      expect(resolvedValue).toBeUndefined();
 *
 *      // Simulate resolving of promise
 *      deferred.resolve(123);
 *      // Note that the 'then' function does not get called synchronously.
 *      // This is because we want the promise API to always be async, whether or not
 *      // it got called synchronously or asynchronously.
 *      expect(resolvedValue).toBeUndefined();
 *
 *      // Propagate promise resolution to 'then' functions using $apply().
 *      $rootScope.$apply();
 *      expect(resolvedValue).toEqual(123);
 *    }));
 *  ```
 *
 * @param {function(function, function)} resolver Function which is responsible for resolving or
 *   rejecting the newly created promise. The first parameter is a function which resolves the
 *   promise, the second parameter is a function which rejects the promise.
 *
 * @returns {Promise} The newly created promise.
 */
/**
 * @ngdoc provider
 * @name $qProvider
 * @this
 *
 * @description
 */
function $QProvider() {
  var errorOnUnhandledRejections = true;
  this.$get = ['$rootScope', '$exceptionHandler', function($rootScope, $exceptionHandler) {
    return qFactory(function(callback) {
      $rootScope.$evalAsync(callback);
    }, $exceptionHandler, errorOnUnhandledRejections);
  }];

  /**
   * @ngdoc method
   * @name $qProvider#errorOnUnhandledRejections
   * @kind function
   *
   * @description
   * Retrieves or overrides whether to generate an error when a rejected promise is not handled.
   * This feature is enabled by default.
   *
   * @param {boolean=} value Whether to generate an error when a rejected promise is not handled.
   * @returns {boolean|ng.$qProvider} Current value when called without a new value or self for
   *    chaining otherwise.
   */
  this.errorOnUnhandledRejections = function(value) {
    if (isDefined(value)) {
      errorOnUnhandledRejections = value;
      return this;
    } else {
      return errorOnUnhandledRejections;
    }
  };
}

/** @this */
function $$QProvider() {
  var errorOnUnhandledRejections = true;
  this.$get = ['$browser', '$exceptionHandler', function($browser, $exceptionHandler) {
    return qFactory(function(callback) {
      $browser.defer(callback);
    }, $exceptionHandler, errorOnUnhandledRejections);
  }];

  this.errorOnUnhandledRejections = function(value) {
    if (isDefined(value)) {
      errorOnUnhandledRejections = value;
      return this;
    } else {
      return errorOnUnhandledRejections;
    }
  };
}

/**
 * Constructs a promise manager.
 *
 * @param {function(function)} nextTick Function for executing functions in the next turn.
 * @param {function(...*)} exceptionHandler Function into which unexpected exceptions are passed for
 *     debugging purposes.
 @ param {=boolean} errorOnUnhandledRejections Whether an error should be generated on unhandled
 *     promises rejections.
 * @returns {object} Promise manager.
 */
function qFactory(nextTick, exceptionHandler, errorOnUnhandledRejections) {
  var $qMinErr = minErr('$q', TypeError);
  var queueSize = 0;
  var checkQueue = [];

  /**
   * @ngdoc method
   * @name ng.$q#defer
   * @kind function
   *
   * @description
   * Creates a `Deferred` object which represents a task which will finish in the future.
   *
   * @returns {Deferred} Returns a new instance of deferred.
   */
  function defer() {
    return new Deferred();
  }

  function Deferred() {
    var promise = this.promise = new Promise();
    //Non prototype methods necessary to support unbound execution :/
    this.resolve = function(val) { resolvePromise(promise, val); };
    this.reject = function(reason) { rejectPromise(promise, reason); };
    this.notify = function(progress) { notifyPromise(promise, progress); };
  }


  function Promise() {
    this.$$state = { status: 0 };
  }

  extend(Promise.prototype, {
    then: function(onFulfilled, onRejected, progressBack) {
      if (isUndefined(onFulfilled) && isUndefined(onRejected) && isUndefined(progressBack)) {
        return this;
      }
      var result = new Promise();

      this.$$state.pending = this.$$state.pending || [];
      this.$$state.pending.push([result, onFulfilled, onRejected, progressBack]);
      if (this.$$state.status > 0) scheduleProcessQueue(this.$$state);

      return result;
    },

    'catch': function(callback) {
      return this.then(null, callback);
    },

    'finally': function(callback, progressBack) {
      return this.then(function(value) {
        return handleCallback(value, resolve, callback);
      }, function(error) {
        return handleCallback(error, reject, callback);
      }, progressBack);
    }
  });

  function processQueue(state) {
    var fn, promise, pending;

    pending = state.pending;
    state.processScheduled = false;
    state.pending = undefined;
    try {
      for (var i = 0, ii = pending.length; i < ii; ++i) {
        state.pur = true;
        promise = pending[i][0];
        fn = pending[i][state.status];
        try {
          if (isFunction(fn)) {
            resolvePromise(promise, fn(state.value));
          } else if (state.status === 1) {
            resolvePromise(promise, state.value);
          } else {
            rejectPromise(promise, state.value);
          }
        } catch (e) {
          rejectPromise(promise, e);
        }
      }
    } finally {
      --queueSize;
      if (errorOnUnhandledRejections && queueSize === 0) {
        nextTick(processChecks);
      }
    }
  }

  function processChecks() {
    // eslint-disable-next-line no-unmodified-loop-condition
    while (!queueSize && checkQueue.length) {
      var toCheck = checkQueue.shift();
      if (!toCheck.pur) {
        toCheck.pur = true;
        var errorMessage = 'Possibly unhandled rejection: ' + toDebugString(toCheck.value);
        if (toCheck.value instanceof Error) {
          exceptionHandler(toCheck.value, errorMessage);
        } else {
          exceptionHandler(errorMessage);
        }
      }
    }
  }

  function scheduleProcessQueue(state) {
    if (errorOnUnhandledRejections && !state.pending && state.status === 2 && !state.pur) {
      if (queueSize === 0 && checkQueue.length === 0) {
        nextTick(processChecks);
      }
      checkQueue.push(state);
    }
    if (state.processScheduled || !state.pending) return;
    state.processScheduled = true;
    ++queueSize;
    nextTick(function() { processQueue(state); });
  }

  function resolvePromise(promise, val) {
    if (promise.$$state.status) return;
    if (val === promise) {
      $$reject(promise, $qMinErr(
        'qcycle',
        'Expected promise to be resolved with value other than itself \'{0}\'',
        val));
    } else {
      $$resolve(promise, val);
    }

  }

  function $$resolve(promise, val) {
    var then;
    var done = false;
    try {
      if (isObject(val) || isFunction(val)) then = val.then;
      if (isFunction(then)) {
        promise.$$state.status = -1;
        then.call(val, doResolve, doReject, doNotify);
      } else {
        promise.$$state.value = val;
        promise.$$state.status = 1;
        scheduleProcessQueue(promise.$$state);
      }
    } catch (e) {
      doReject(e);
    }

    function doResolve(val) {
      if (done) return;
      done = true;
      $$resolve(promise, val);
    }
    function doReject(val) {
      if (done) return;
      done = true;
      $$reject(promise, val);
    }
    function doNotify(progress) {
      notifyPromise(promise, progress);
    }
  }

  function rejectPromise(promise, reason) {
    if (promise.$$state.status) return;
    $$reject(promise, reason);
  }

  function $$reject(promise, reason) {
    promise.$$state.value = reason;
    promise.$$state.status = 2;
    scheduleProcessQueue(promise.$$state);
  }

  function notifyPromise(promise, progress) {
    var callbacks = promise.$$state.pending;

    if ((promise.$$state.status <= 0) && callbacks && callbacks.length) {
      nextTick(function() {
        var callback, result;
        for (var i = 0, ii = callbacks.length; i < ii; i++) {
          result = callbacks[i][0];
          callback = callbacks[i][3];
          try {
            notifyPromise(result, isFunction(callback) ? callback(progress) : progress);
          } catch (e) {
            exceptionHandler(e);
          }
        }
      });
    }
  }

  /**
   * @ngdoc method
   * @name $q#reject
   * @kind function
   *
   * @description
   * Creates a promise that is resolved as rejected with the specified `reason`. This api should be
   * used to forward rejection in a chain of promises. If you are dealing with the last promise in
   * a promise chain, you don't need to worry about it.
   *
   * When comparing deferreds/promises to the familiar behavior of try/catch/throw, think of
   * `reject` as the `throw` keyword in JavaScript. This also means that if you "catch" an error via
   * a promise error callback and you want to forward the error to the promise derived from the
   * current promise, you have to "rethrow" the error by returning a rejection constructed via
   * `reject`.
   *
   * ```js
   *   promiseB = promiseA.then(function(result) {
   *     // success: do something and resolve promiseB
   *     //          with the old or a new result
   *     return result;
   *   }, function(reason) {
   *     // error: handle the error if possible and
   *     //        resolve promiseB with newPromiseOrValue,
   *     //        otherwise forward the rejection to promiseB
   *     if (canHandle(reason)) {
   *      // handle the error and recover
   *      return newPromiseOrValue;
   *     }
   *     return $q.reject(reason);
   *   });
   * ```
   *
   * @param {*} reason Constant, message, exception or an object representing the rejection reason.
   * @returns {Promise} Returns a promise that was already resolved as rejected with the `reason`.
   */
  function reject(reason) {
    var result = new Promise();
    rejectPromise(result, reason);
    return result;
  }

  function handleCallback(value, resolver, callback) {
    var callbackOutput = null;
    try {
      if (isFunction(callback)) callbackOutput = callback();
    } catch (e) {
      return reject(e);
    }
    if (isPromiseLike(callbackOutput)) {
      return callbackOutput.then(function() {
        return resolver(value);
      }, reject);
    } else {
      return resolver(value);
    }
  }

  /**
   * @ngdoc method
   * @name $q#when
   * @kind function
   *
   * @description
   * Wraps an object that might be a value or a (3rd party) then-able promise into a $q promise.
   * This is useful when you are dealing with an object that might or might not be a promise, or if
   * the promise comes from a source that can't be trusted.
   *
   * @param {*} value Value or a promise
   * @param {Function=} successCallback
   * @param {Function=} errorCallback
   * @param {Function=} progressCallback
   * @returns {Promise} Returns a promise of the passed value or promise
   */


  function when(value, callback, errback, progressBack) {
    var result = new Promise();
    resolvePromise(result, value);
    return result.then(callback, errback, progressBack);
  }

  /**
   * @ngdoc method
   * @name $q#resolve
   * @kind function
   *
   * @description
   * Alias of {@link ng.$q#when when} to maintain naming consistency with ES6.
   *
   * @param {*} value Value or a promise
   * @param {Function=} successCallback
   * @param {Function=} errorCallback
   * @param {Function=} progressCallback
   * @returns {Promise} Returns a promise of the passed value or promise
   */
  var resolve = when;

  /**
   * @ngdoc method
   * @name $q#all
   * @kind function
   *
   * @description
   * Combines multiple promises into a single promise that is resolved when all of the input
   * promises are resolved.
   *
   * @param {Array.<Promise>|Object.<Promise>} promises An array or hash of promises.
   * @returns {Promise} Returns a single promise that will be resolved with an array/hash of values,
   *   each value corresponding to the promise at the same index/key in the `promises` array/hash.
   *   If any of the promises is resolved with a rejection, this resulting promise will be rejected
   *   with the same rejection value.
   */

  function all(promises) {
    var result = new Promise(),
        counter = 0,
        results = isArray(promises) ? [] : {};

    forEach(promises, function(promise, key) {
      counter++;
      when(promise).then(function(value) {
        results[key] = value;
        if (!(--counter)) resolvePromise(result, results);
      }, function(reason) {
        rejectPromise(result, reason);
      });
    });

    if (counter === 0) {
      resolvePromise(result, results);
    }

    return result;
  }

  /**
   * @ngdoc method
   * @name $q#race
   * @kind function
   *
   * @description
   * Returns a promise that resolves or rejects as soon as one of those promises
   * resolves or rejects, with the value or reason from that promise.
   *
   * @param {Array.<Promise>|Object.<Promise>} promises An array or hash of promises.
   * @returns {Promise} a promise that resolves or rejects as soon as one of the `promises`
   * resolves or rejects, with the value or reason from that promise.
   */

  function race(promises) {
    var deferred = defer();

    forEach(promises, function(promise) {
      when(promise).then(deferred.resolve, deferred.reject);
    });

    return deferred.promise;
  }

  function $Q(resolver) {
    if (!isFunction(resolver)) {
      throw $qMinErr('norslvr', 'Expected resolverFn, got \'{0}\'', resolver);
    }

    var promise = new Promise();

    function resolveFn(value) {
      resolvePromise(promise, value);
    }

    function rejectFn(reason) {
      rejectPromise(promise, reason);
    }

    resolver(resolveFn, rejectFn);

    return promise;
  }

  // Let's make the instanceof operator work for promises, so that
  // `new $q(fn) instanceof $q` would evaluate to true.
  $Q.prototype = Promise.prototype;

  $Q.defer = defer;
  $Q.reject = reject;
  $Q.when = when;
  $Q.resolve = resolve;
  $Q.all = all;
  $Q.race = race;

  return $Q;
}

/** @this */
function $$RAFProvider() { //rAF
  this.$get = ['$window', '$timeout', function($window, $timeout) {
    var requestAnimationFrame = $window.requestAnimationFrame ||
                                $window.webkitRequestAnimationFrame;

    var cancelAnimationFrame = $window.cancelAnimationFrame ||
                               $window.webkitCancelAnimationFrame ||
                               $window.webkitCancelRequestAnimationFrame;

    var rafSupported = !!requestAnimationFrame;
    var raf = rafSupported
      ? function(fn) {
          var id = requestAnimationFrame(fn);
          return function() {
            cancelAnimationFrame(id);
          };
        }
      : function(fn) {
          var timer = $timeout(fn, 16.66, false); // 1000 / 60 = 16.666
          return function() {
            $timeout.cancel(timer);
          };
        };

    raf.supported = rafSupported;

    return raf;
  }];
}

/**
 * DESIGN NOTES
 *
 * The design decisions behind the scope are heavily favored for speed and memory consumption.
 *
 * The typical use of scope is to watch the expressions, which most of the time return the same
 * value as last time so we optimize the operation.
 *
 * Closures construction is expensive in terms of speed as well as memory:
 *   - No closures, instead use prototypical inheritance for API
 *   - Internal state needs to be stored on scope directly, which means that private state is
 *     exposed as $$____ properties
 *
 * Loop operations are optimized by using while(count--) { ... }
 *   - This means that in order to keep the same order of execution as addition we have to add
 *     items to the array at the beginning (unshift) instead of at the end (push)
 *
 * Child scopes are created and removed often
 *   - Using an array would be slow since inserts in the middle are expensive; so we use linked lists
 *
 * There are fewer watches than observers. This is why you don't want the observer to be implemented
 * in the same way as watch. Watch requires return of the initialization function which is expensive
 * to construct.
 */


/**
 * @ngdoc provider
 * @name $rootScopeProvider
 * @description
 *
 * Provider for the $rootScope service.
 */

/**
 * @ngdoc method
 * @name $rootScopeProvider#digestTtl
 * @description
 *
 * Sets the number of `$digest` iterations the scope should attempt to execute before giving up and
 * assuming that the model is unstable.
 *
 * The current default is 10 iterations.
 *
 * In complex applications it's possible that the dependencies between `$watch`s will result in
 * several digest iterations. However if an application needs more than the default 10 digest
 * iterations for its model to stabilize then you should investigate what is causing the model to
 * continuously change during the digest.
 *
 * Increasing the TTL could have performance implications, so you should not change it without
 * proper justification.
 *
 * @param {number} limit The number of digest iterations.
 */


/**
 * @ngdoc service
 * @name $rootScope
 * @this
 *
 * @description
 *
 * Every application has a single root {@link ng.$rootScope.Scope scope}.
 * All other scopes are descendant scopes of the root scope. Scopes provide separation
 * between the model and the view, via a mechanism for watching the model for changes.
 * They also provide event emission/broadcast and subscription facility. See the
 * {@link guide/scope developer guide on scopes}.
 */
function $RootScopeProvider() {
  var TTL = 10;
  var $rootScopeMinErr = minErr('$rootScope');
  var lastDirtyWatch = null;
  var applyAsyncId = null;

  this.digestTtl = function(value) {
    if (arguments.length) {
      TTL = value;
    }
    return TTL;
  };

  function createChildScopeClass(parent) {
    function ChildScope() {
      this.$$watchers = this.$$nextSibling =
          this.$$childHead = this.$$childTail = null;
      this.$$listeners = {};
      this.$$listenerCount = {};
      this.$$watchersCount = 0;
      this.$id = nextUid();
      this.$$ChildScope = null;
    }
    ChildScope.prototype = parent;
    return ChildScope;
  }

  this.$get = ['$exceptionHandler', '$parse', '$browser',
      function($exceptionHandler, $parse, $browser) {

    function destroyChildScope($event) {
        $event.currentScope.$$destroyed = true;
    }

    function cleanUpScope($scope) {

      // Support: IE 9 only
      if (msie === 9) {
        // There is a memory leak in IE9 if all child scopes are not disconnected
        // completely when a scope is destroyed. So this code will recurse up through
        // all this scopes children
        //
        // See issue https://github.com/angular/angular.js/issues/10706
        if ($scope.$$childHead) {
          cleanUpScope($scope.$$childHead);
        }
        if ($scope.$$nextSibling) {
          cleanUpScope($scope.$$nextSibling);
        }
      }

      // The code below works around IE9 and V8's memory leaks
      //
      // See:
      // - https://code.google.com/p/v8/issues/detail?id=2073#c26
      // - https://github.com/angular/angular.js/issues/6794#issuecomment-38648909
      // - https://github.com/angular/angular.js/issues/1313#issuecomment-10378451

      $scope.$parent = $scope.$$nextSibling = $scope.$$prevSibling = $scope.$$childHead =
          $scope.$$childTail = $scope.$root = $scope.$$watchers = null;
    }

    /**
     * @ngdoc type
     * @name $rootScope.Scope
     *
     * @description
     * A root scope can be retrieved using the {@link ng.$rootScope $rootScope} key from the
     * {@link auto.$injector $injector}. Child scopes are created using the
     * {@link ng.$rootScope.Scope#$new $new()} method. (Most scopes are created automatically when
     * compiled HTML template is executed.) See also the {@link guide/scope Scopes guide} for
     * an in-depth introduction and usage examples.
     *
     *
     * # Inheritance
     * A scope can inherit from a parent scope, as in this example:
     * ```js
         var parent = $rootScope;
         var child = parent.$new();

         parent.salutation = "Hello";
         expect(child.salutation).toEqual('Hello');

         child.salutation = "Welcome";
         expect(child.salutation).toEqual('Welcome');
         expect(parent.salutation).toEqual('Hello');
     * ```
     *
     * When interacting with `Scope` in tests, additional helper methods are available on the
     * instances of `Scope` type. See {@link ngMock.$rootScope.Scope ngMock Scope} for additional
     * details.
     *
     *
     * @param {Object.<string, function()>=} providers Map of service factory which need to be
     *                                       provided for the current scope. Defaults to {@link ng}.
     * @param {Object.<string, *>=} instanceCache Provides pre-instantiated services which should
     *                              append/override services provided by `providers`. This is handy
     *                              when unit-testing and having the need to override a default
     *                              service.
     * @returns {Object} Newly created scope.
     *
     */
    function Scope() {
      this.$id = nextUid();
      this.$$phase = this.$parent = this.$$watchers =
                     this.$$nextSibling = this.$$prevSibling =
                     this.$$childHead = this.$$childTail = null;
      this.$root = this;
      this.$$destroyed = false;
      this.$$listeners = {};
      this.$$listenerCount = {};
      this.$$watchersCount = 0;
      this.$$isolateBindings = null;
    }

    /**
     * @ngdoc property
     * @name $rootScope.Scope#$id
     *
     * @description
     * Unique scope ID (monotonically increasing) useful for debugging.
     */

     /**
      * @ngdoc property
      * @name $rootScope.Scope#$parent
      *
      * @description
      * Reference to the parent scope.
      */

      /**
       * @ngdoc property
       * @name $rootScope.Scope#$root
       *
       * @description
       * Reference to the root scope.
       */

    Scope.prototype = {
      constructor: Scope,
      /**
       * @ngdoc method
       * @name $rootScope.Scope#$new
       * @kind function
       *
       * @description
       * Creates a new child {@link ng.$rootScope.Scope scope}.
       *
       * The parent scope will propagate the {@link ng.$rootScope.Scope#$digest $digest()} event.
       * The scope can be removed from the scope hierarchy using {@link ng.$rootScope.Scope#$destroy $destroy()}.
       *
       * {@link ng.$rootScope.Scope#$destroy $destroy()} must be called on a scope when it is
       * desired for the scope and its child scopes to be permanently detached from the parent and
       * thus stop participating in model change detection and listener notification by invoking.
       *
       * @param {boolean} isolate If true, then the scope does not prototypically inherit from the
       *         parent scope. The scope is isolated, as it can not see parent scope properties.
       *         When creating widgets, it is useful for the widget to not accidentally read parent
       *         state.
       *
       * @param {Scope} [parent=this] The {@link ng.$rootScope.Scope `Scope`} that will be the `$parent`
       *                              of the newly created scope. Defaults to `this` scope if not provided.
       *                              This is used when creating a transclude scope to correctly place it
       *                              in the scope hierarchy while maintaining the correct prototypical
       *                              inheritance.
       *
       * @returns {Object} The newly created child scope.
       *
       */
      $new: function(isolate, parent) {
        var child;

        parent = parent || this;

        if (isolate) {
          child = new Scope();
          child.$root = this.$root;
        } else {
          // Only create a child scope class if somebody asks for one,
          // but cache it to allow the VM to optimize lookups.
          if (!this.$$ChildScope) {
            this.$$ChildScope = createChildScopeClass(this);
          }
          child = new this.$$ChildScope();
        }
        child.$parent = parent;
        child.$$prevSibling = parent.$$childTail;
        if (parent.$$childHead) {
          parent.$$childTail.$$nextSibling = child;
          parent.$$childTail = child;
        } else {
          parent.$$childHead = parent.$$childTail = child;
        }

        // When the new scope is not isolated or we inherit from `this`, and
        // the parent scope is destroyed, the property `$$destroyed` is inherited
        // prototypically. In all other cases, this property needs to be set
        // when the parent scope is destroyed.
        // The listener needs to be added after the parent is set
        if (isolate || parent !== this) child.$on('$destroy', destroyChildScope);

        return child;
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$watch
       * @kind function
       *
       * @description
       * Registers a `listener` callback to be executed whenever the `watchExpression` changes.
       *
       * - The `watchExpression` is called on every call to {@link ng.$rootScope.Scope#$digest
       *   $digest()} and should return the value that will be watched. (`watchExpression` should not change
       *   its value when executed multiple times with the same input because it may be executed multiple
       *   times by {@link ng.$rootScope.Scope#$digest $digest()}. That is, `watchExpression` should be
       *   [idempotent](http://en.wikipedia.org/wiki/Idempotence).)
       * - The `listener` is called only when the value from the current `watchExpression` and the
       *   previous call to `watchExpression` are not equal (with the exception of the initial run,
       *   see below). Inequality is determined according to reference inequality,
       *   [strict comparison](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Comparison_Operators)
       *    via the `!==` Javascript operator, unless `objectEquality == true`
       *   (see next point)
       * - When `objectEquality == true`, inequality of the `watchExpression` is determined
       *   according to the {@link angular.equals} function. To save the value of the object for
       *   later comparison, the {@link angular.copy} function is used. This therefore means that
       *   watching complex objects will have adverse memory and performance implications.
       * - This should not be used to watch for changes in objects that are
       *   or contain [File](https://developer.mozilla.org/docs/Web/API/File) objects due to limitations with {@link angular.copy `angular.copy`}.
       * - The watch `listener` may change the model, which may trigger other `listener`s to fire.
       *   This is achieved by rerunning the watchers until no changes are detected. The rerun
       *   iteration limit is 10 to prevent an infinite loop deadlock.
       *
       *
       * If you want to be notified whenever {@link ng.$rootScope.Scope#$digest $digest} is called,
       * you can register a `watchExpression` function with no `listener`. (Be prepared for
       * multiple calls to your `watchExpression` because it will execute multiple times in a
       * single {@link ng.$rootScope.Scope#$digest $digest} cycle if a change is detected.)
       *
       * After a watcher is registered with the scope, the `listener` fn is called asynchronously
       * (via {@link ng.$rootScope.Scope#$evalAsync $evalAsync}) to initialize the
       * watcher. In rare cases, this is undesirable because the listener is called when the result
       * of `watchExpression` didn't change. To detect this scenario within the `listener` fn, you
       * can compare the `newVal` and `oldVal`. If these two values are identical (`===`) then the
       * listener was called due to initialization.
       *
       *
       *
       * # Example
       * ```js
           // let's assume that scope was dependency injected as the $rootScope
           var scope = $rootScope;
           scope.name = 'misko';
           scope.counter = 0;

           expect(scope.counter).toEqual(0);
           scope.$watch('name', function(newValue, oldValue) {
             scope.counter = scope.counter + 1;
           });
           expect(scope.counter).toEqual(0);

           scope.$digest();
           // the listener is always called during the first $digest loop after it was registered
           expect(scope.counter).toEqual(1);

           scope.$digest();
           // but now it will not be called unless the value changes
           expect(scope.counter).toEqual(1);

           scope.name = 'adam';
           scope.$digest();
           expect(scope.counter).toEqual(2);



           // Using a function as a watchExpression
           var food;
           scope.foodCounter = 0;
           expect(scope.foodCounter).toEqual(0);
           scope.$watch(
             // This function returns the value being watched. It is called for each turn of the $digest loop
             function() { return food; },
             // This is the change listener, called when the value returned from the above function changes
             function(newValue, oldValue) {
               if ( newValue !== oldValue ) {
                 // Only increment the counter if the value changed
                 scope.foodCounter = scope.foodCounter + 1;
               }
             }
           );
           // No digest has been run so the counter will be zero
           expect(scope.foodCounter).toEqual(0);

           // Run the digest but since food has not changed count will still be zero
           scope.$digest();
           expect(scope.foodCounter).toEqual(0);

           // Update food and run digest.  Now the counter will increment
           food = 'cheeseburger';
           scope.$digest();
           expect(scope.foodCounter).toEqual(1);

       * ```
       *
       *
       *
       * @param {(function()|string)} watchExpression Expression that is evaluated on each
       *    {@link ng.$rootScope.Scope#$digest $digest} cycle. A change in the return value triggers
       *    a call to the `listener`.
       *
       *    - `string`: Evaluated as {@link guide/expression expression}
       *    - `function(scope)`: called with current `scope` as a parameter.
       * @param {function(newVal, oldVal, scope)} listener Callback called whenever the value
       *    of `watchExpression` changes.
       *
       *    - `newVal` contains the current value of the `watchExpression`
       *    - `oldVal` contains the previous value of the `watchExpression`
       *    - `scope` refers to the current scope
       * @param {boolean=} [objectEquality=false] Compare for object equality using {@link angular.equals} instead of
       *     comparing for reference equality.
       * @returns {function()} Returns a deregistration function for this listener.
       */
      $watch: function(watchExp, listener, objectEquality, prettyPrintExpression) {
        var get = $parse(watchExp);

        if (get.$$watchDelegate) {
          return get.$$watchDelegate(this, listener, objectEquality, get, watchExp);
        }
        var scope = this,
            array = scope.$$watchers,
            watcher = {
              fn: listener,
              last: initWatchVal,
              get: get,
              exp: prettyPrintExpression || watchExp,
              eq: !!objectEquality
            };

        lastDirtyWatch = null;

        if (!isFunction(listener)) {
          watcher.fn = noop;
        }

        if (!array) {
          array = scope.$$watchers = [];
          array.$$digestWatchIndex = -1;
        }
        // we use unshift since we use a while loop in $digest for speed.
        // the while loop reads in reverse order.
        array.unshift(watcher);
        array.$$digestWatchIndex++;
        incrementWatchersCount(this, 1);

        return function deregisterWatch() {
          var index = arrayRemove(array, watcher);
          if (index >= 0) {
            incrementWatchersCount(scope, -1);
            if (index < array.$$digestWatchIndex) {
              array.$$digestWatchIndex--;
            }
          }
          lastDirtyWatch = null;
        };
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$watchGroup
       * @kind function
       *
       * @description
       * A variant of {@link ng.$rootScope.Scope#$watch $watch()} where it watches an array of `watchExpressions`.
       * If any one expression in the collection changes the `listener` is executed.
       *
       * - The items in the `watchExpressions` array are observed via the standard `$watch` operation. Their return
       *   values are examined for changes on every call to `$digest`.
       * - The `listener` is called whenever any expression in the `watchExpressions` array changes.
       *
       * @param {Array.<string|Function(scope)>} watchExpressions Array of expressions that will be individually
       * watched using {@link ng.$rootScope.Scope#$watch $watch()}
       *
       * @param {function(newValues, oldValues, scope)} listener Callback called whenever the return value of any
       *    expression in `watchExpressions` changes
       *    The `newValues` array contains the current values of the `watchExpressions`, with the indexes matching
       *    those of `watchExpression`
       *    and the `oldValues` array contains the previous values of the `watchExpressions`, with the indexes matching
       *    those of `watchExpression`
       *    The `scope` refers to the current scope.
       * @returns {function()} Returns a de-registration function for all listeners.
       */
      $watchGroup: function(watchExpressions, listener) {
        var oldValues = new Array(watchExpressions.length);
        var newValues = new Array(watchExpressions.length);
        var deregisterFns = [];
        var self = this;
        var changeReactionScheduled = false;
        var firstRun = true;

        if (!watchExpressions.length) {
          // No expressions means we call the listener ASAP
          var shouldCall = true;
          self.$evalAsync(function() {
            if (shouldCall) listener(newValues, newValues, self);
          });
          return function deregisterWatchGroup() {
            shouldCall = false;
          };
        }

        if (watchExpressions.length === 1) {
          // Special case size of one
          return this.$watch(watchExpressions[0], function watchGroupAction(value, oldValue, scope) {
            newValues[0] = value;
            oldValues[0] = oldValue;
            listener(newValues, (value === oldValue) ? newValues : oldValues, scope);
          });
        }

        forEach(watchExpressions, function(expr, i) {
          var unwatchFn = self.$watch(expr, function watchGroupSubAction(value, oldValue) {
            newValues[i] = value;
            oldValues[i] = oldValue;
            if (!changeReactionScheduled) {
              changeReactionScheduled = true;
              self.$evalAsync(watchGroupAction);
            }
          });
          deregisterFns.push(unwatchFn);
        });

        function watchGroupAction() {
          changeReactionScheduled = false;

          if (firstRun) {
            firstRun = false;
            listener(newValues, newValues, self);
          } else {
            listener(newValues, oldValues, self);
          }
        }

        return function deregisterWatchGroup() {
          while (deregisterFns.length) {
            deregisterFns.shift()();
          }
        };
      },


      /**
       * @ngdoc method
       * @name $rootScope.Scope#$watchCollection
       * @kind function
       *
       * @description
       * Shallow watches the properties of an object and fires whenever any of the properties change
       * (for arrays, this implies watching the array items; for object maps, this implies watching
       * the properties). If a change is detected, the `listener` callback is fired.
       *
       * - The `obj` collection is observed via standard $watch operation and is examined on every
       *   call to $digest() to see if any items have been added, removed, or moved.
       * - The `listener` is called whenever anything within the `obj` has changed. Examples include
       *   adding, removing, and moving items belonging to an object or array.
       *
       *
       * # Example
       * ```js
          $scope.names = ['igor', 'matias', 'misko', 'james'];
          $scope.dataCount = 4;

          $scope.$watchCollection('names', function(newNames, oldNames) {
            $scope.dataCount = newNames.length;
          });

          expect($scope.dataCount).toEqual(4);
          $scope.$digest();

          //still at 4 ... no changes
          expect($scope.dataCount).toEqual(4);

          $scope.names.pop();
          $scope.$digest();

          //now there's been a change
          expect($scope.dataCount).toEqual(3);
       * ```
       *
       *
       * @param {string|function(scope)} obj Evaluated as {@link guide/expression expression}. The
       *    expression value should evaluate to an object or an array which is observed on each
       *    {@link ng.$rootScope.Scope#$digest $digest} cycle. Any shallow change within the
       *    collection will trigger a call to the `listener`.
       *
       * @param {function(newCollection, oldCollection, scope)} listener a callback function called
       *    when a change is detected.
       *    - The `newCollection` object is the newly modified data obtained from the `obj` expression
       *    - The `oldCollection` object is a copy of the former collection data.
       *      Due to performance considerations, the`oldCollection` value is computed only if the
       *      `listener` function declares two or more arguments.
       *    - The `scope` argument refers to the current scope.
       *
       * @returns {function()} Returns a de-registration function for this listener. When the
       *    de-registration function is executed, the internal watch operation is terminated.
       */
      $watchCollection: function(obj, listener) {
        $watchCollectionInterceptor.$stateful = true;

        var self = this;
        // the current value, updated on each dirty-check run
        var newValue;
        // a shallow copy of the newValue from the last dirty-check run,
        // updated to match newValue during dirty-check run
        var oldValue;
        // a shallow copy of the newValue from when the last change happened
        var veryOldValue;
        // only track veryOldValue if the listener is asking for it
        var trackVeryOldValue = (listener.length > 1);
        var changeDetected = 0;
        var changeDetector = $parse(obj, $watchCollectionInterceptor);
        var internalArray = [];
        var internalObject = {};
        var initRun = true;
        var oldLength = 0;

        function $watchCollectionInterceptor(_value) {
          newValue = _value;
          var newLength, key, bothNaN, newItem, oldItem;

          // If the new value is undefined, then return undefined as the watch may be a one-time watch
          if (isUndefined(newValue)) return;

          if (!isObject(newValue)) { // if primitive
            if (oldValue !== newValue) {
              oldValue = newValue;
              changeDetected++;
            }
          } else if (isArrayLike(newValue)) {
            if (oldValue !== internalArray) {
              // we are transitioning from something which was not an array into array.
              oldValue = internalArray;
              oldLength = oldValue.length = 0;
              changeDetected++;
            }

            newLength = newValue.length;

            if (oldLength !== newLength) {
              // if lengths do not match we need to trigger change notification
              changeDetected++;
              oldValue.length = oldLength = newLength;
            }
            // copy the items to oldValue and look for changes.
            for (var i = 0; i < newLength; i++) {
              oldItem = oldValue[i];
              newItem = newValue[i];

              // eslint-disable-next-line no-self-compare
              bothNaN = (oldItem !== oldItem) && (newItem !== newItem);
              if (!bothNaN && (oldItem !== newItem)) {
                changeDetected++;
                oldValue[i] = newItem;
              }
            }
          } else {
            if (oldValue !== internalObject) {
              // we are transitioning from something which was not an object into object.
              oldValue = internalObject = {};
              oldLength = 0;
              changeDetected++;
            }
            // copy the items to oldValue and look for changes.
            newLength = 0;
            for (key in newValue) {
              if (hasOwnProperty.call(newValue, key)) {
                newLength++;
                newItem = newValue[key];
                oldItem = oldValue[key];

                if (key in oldValue) {
                  // eslint-disable-next-line no-self-compare
                  bothNaN = (oldItem !== oldItem) && (newItem !== newItem);
                  if (!bothNaN && (oldItem !== newItem)) {
                    changeDetected++;
                    oldValue[key] = newItem;
                  }
                } else {
                  oldLength++;
                  oldValue[key] = newItem;
                  changeDetected++;
                }
              }
            }
            if (oldLength > newLength) {
              // we used to have more keys, need to find them and destroy them.
              changeDetected++;
              for (key in oldValue) {
                if (!hasOwnProperty.call(newValue, key)) {
                  oldLength--;
                  delete oldValue[key];
                }
              }
            }
          }
          return changeDetected;
        }

        function $watchCollectionAction() {
          if (initRun) {
            initRun = false;
            listener(newValue, newValue, self);
          } else {
            listener(newValue, veryOldValue, self);
          }

          // make a copy for the next time a collection is changed
          if (trackVeryOldValue) {
            if (!isObject(newValue)) {
              //primitive
              veryOldValue = newValue;
            } else if (isArrayLike(newValue)) {
              veryOldValue = new Array(newValue.length);
              for (var i = 0; i < newValue.length; i++) {
                veryOldValue[i] = newValue[i];
              }
            } else { // if object
              veryOldValue = {};
              for (var key in newValue) {
                if (hasOwnProperty.call(newValue, key)) {
                  veryOldValue[key] = newValue[key];
                }
              }
            }
          }
        }

        return this.$watch(changeDetector, $watchCollectionAction);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$digest
       * @kind function
       *
       * @description
       * Processes all of the {@link ng.$rootScope.Scope#$watch watchers} of the current scope and
       * its children. Because a {@link ng.$rootScope.Scope#$watch watcher}'s listener can change
       * the model, the `$digest()` keeps calling the {@link ng.$rootScope.Scope#$watch watchers}
       * until no more listeners are firing. This means that it is possible to get into an infinite
       * loop. This function will throw `'Maximum iteration limit exceeded.'` if the number of
       * iterations exceeds 10.
       *
       * Usually, you don't call `$digest()` directly in
       * {@link ng.directive:ngController controllers} or in
       * {@link ng.$compileProvider#directive directives}.
       * Instead, you should call {@link ng.$rootScope.Scope#$apply $apply()} (typically from within
       * a {@link ng.$compileProvider#directive directive}), which will force a `$digest()`.
       *
       * If you want to be notified whenever `$digest()` is called,
       * you can register a `watchExpression` function with
       * {@link ng.$rootScope.Scope#$watch $watch()} with no `listener`.
       *
       * In unit tests, you may need to call `$digest()` to simulate the scope life cycle.
       *
       * # Example
       * ```js
           var scope = ...;
           scope.name = 'misko';
           scope.counter = 0;

           expect(scope.counter).toEqual(0);
           scope.$watch('name', function(newValue, oldValue) {
             scope.counter = scope.counter + 1;
           });
           expect(scope.counter).toEqual(0);

           scope.$digest();
           // the listener is always called during the first $digest loop after it was registered
           expect(scope.counter).toEqual(1);

           scope.$digest();
           // but now it will not be called unless the value changes
           expect(scope.counter).toEqual(1);

           scope.name = 'adam';
           scope.$digest();
           expect(scope.counter).toEqual(2);
       * ```
       *
       */
      $digest: function() {
        var watch, value, last, fn, get,
            watchers,
            dirty, ttl = TTL,
            next, current, target = this,
            watchLog = [],
            logIdx, asyncTask;

        beginPhase('$digest');
        // Check for changes to browser url that happened in sync before the call to $digest
        $browser.$$checkUrlChange();

        if (this === $rootScope && applyAsyncId !== null) {
          // If this is the root scope, and $applyAsync has scheduled a deferred $apply(), then
          // cancel the scheduled $apply and flush the queue of expressions to be evaluated.
          $browser.defer.cancel(applyAsyncId);
          flushApplyAsync();
        }

        lastDirtyWatch = null;

        do { // "while dirty" loop
          dirty = false;
          current = target;

          // It's safe for asyncQueuePosition to be a local variable here because this loop can't
          // be reentered recursively. Calling $digest from a function passed to $evalAsync would
          // lead to a '$digest already in progress' error.
          for (var asyncQueuePosition = 0; asyncQueuePosition < asyncQueue.length; asyncQueuePosition++) {
            try {
              asyncTask = asyncQueue[asyncQueuePosition];
              fn = asyncTask.fn;
              fn(asyncTask.scope, asyncTask.locals);
            } catch (e) {
              $exceptionHandler(e);
            }
            lastDirtyWatch = null;
          }
          asyncQueue.length = 0;

          traverseScopesLoop:
          do { // "traverse the scopes" loop
            if ((watchers = current.$$watchers)) {
              // process our watches
              watchers.$$digestWatchIndex = watchers.length;
              while (watchers.$$digestWatchIndex--) {
                try {
                  watch = watchers[watchers.$$digestWatchIndex];
                  // Most common watches are on primitives, in which case we can short
                  // circuit it with === operator, only when === fails do we use .equals
                  if (watch) {
                    get = watch.get;
                    if ((value = get(current)) !== (last = watch.last) &&
                        !(watch.eq
                            ? equals(value, last)
                            : (isNumberNaN(value) && isNumberNaN(last)))) {
                      dirty = true;
                      lastDirtyWatch = watch;
                      watch.last = watch.eq ? copy(value, null) : value;
                      fn = watch.fn;
                      fn(value, ((last === initWatchVal) ? value : last), current);
                      if (ttl < 5) {
                        logIdx = 4 - ttl;
                        if (!watchLog[logIdx]) watchLog[logIdx] = [];
                        watchLog[logIdx].push({
                          msg: isFunction(watch.exp) ? 'fn: ' + (watch.exp.name || watch.exp.toString()) : watch.exp,
                          newVal: value,
                          oldVal: last
                        });
                      }
                    } else if (watch === lastDirtyWatch) {
                      // If the most recently dirty watcher is now clean, short circuit since the remaining watchers
                      // have already been tested.
                      dirty = false;
                      break traverseScopesLoop;
                    }
                  }
                } catch (e) {
                  $exceptionHandler(e);
                }
              }
            }

            // Insanity Warning: scope depth-first traversal
            // yes, this code is a bit crazy, but it works and we have tests to prove it!
            // this piece should be kept in sync with the traversal in $broadcast
            if (!(next = ((current.$$watchersCount && current.$$childHead) ||
                (current !== target && current.$$nextSibling)))) {
              while (current !== target && !(next = current.$$nextSibling)) {
                current = current.$parent;
              }
            }
          } while ((current = next));

          // `break traverseScopesLoop;` takes us to here

          if ((dirty || asyncQueue.length) && !(ttl--)) {
            clearPhase();
            throw $rootScopeMinErr('infdig',
                '{0} $digest() iterations reached. Aborting!\n' +
                'Watchers fired in the last 5 iterations: {1}',
                TTL, watchLog);
          }

        } while (dirty || asyncQueue.length);

        clearPhase();

        // postDigestQueuePosition isn't local here because this loop can be reentered recursively.
        while (postDigestQueuePosition < postDigestQueue.length) {
          try {
            postDigestQueue[postDigestQueuePosition++]();
          } catch (e) {
            $exceptionHandler(e);
          }
        }
        postDigestQueue.length = postDigestQueuePosition = 0;

        // Check for changes to browser url that happened during the $digest
        // (for which no event is fired; e.g. via `history.pushState()`)
        $browser.$$checkUrlChange();
      },


      /**
       * @ngdoc event
       * @name $rootScope.Scope#$destroy
       * @eventType broadcast on scope being destroyed
       *
       * @description
       * Broadcasted when a scope and its children are being destroyed.
       *
       * Note that, in AngularJS, there is also a `$destroy` jQuery event, which can be used to
       * clean up DOM bindings before an element is removed from the DOM.
       */

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$destroy
       * @kind function
       *
       * @description
       * Removes the current scope (and all of its children) from the parent scope. Removal implies
       * that calls to {@link ng.$rootScope.Scope#$digest $digest()} will no longer
       * propagate to the current scope and its children. Removal also implies that the current
       * scope is eligible for garbage collection.
       *
       * The `$destroy()` is usually used by directives such as
       * {@link ng.directive:ngRepeat ngRepeat} for managing the
       * unrolling of the loop.
       *
       * Just before a scope is destroyed, a `$destroy` event is broadcasted on this scope.
       * Application code can register a `$destroy` event handler that will give it a chance to
       * perform any necessary cleanup.
       *
       * Note that, in AngularJS, there is also a `$destroy` jQuery event, which can be used to
       * clean up DOM bindings before an element is removed from the DOM.
       */
      $destroy: function() {
        // We can't destroy a scope that has been already destroyed.
        if (this.$$destroyed) return;
        var parent = this.$parent;

        this.$broadcast('$destroy');
        this.$$destroyed = true;

        if (this === $rootScope) {
          //Remove handlers attached to window when $rootScope is removed
          $browser.$$applicationDestroyed();
        }

        incrementWatchersCount(this, -this.$$watchersCount);
        for (var eventName in this.$$listenerCount) {
          decrementListenerCount(this, this.$$listenerCount[eventName], eventName);
        }

        // sever all the references to parent scopes (after this cleanup, the current scope should
        // not be retained by any of our references and should be eligible for garbage collection)
        if (parent && parent.$$childHead === this) parent.$$childHead = this.$$nextSibling;
        if (parent && parent.$$childTail === this) parent.$$childTail = this.$$prevSibling;
        if (this.$$prevSibling) this.$$prevSibling.$$nextSibling = this.$$nextSibling;
        if (this.$$nextSibling) this.$$nextSibling.$$prevSibling = this.$$prevSibling;

        // Disable listeners, watchers and apply/digest methods
        this.$destroy = this.$digest = this.$apply = this.$evalAsync = this.$applyAsync = noop;
        this.$on = this.$watch = this.$watchGroup = function() { return noop; };
        this.$$listeners = {};

        // Disconnect the next sibling to prevent `cleanUpScope` destroying those too
        this.$$nextSibling = null;
        cleanUpScope(this);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$eval
       * @kind function
       *
       * @description
       * Executes the `expression` on the current scope and returns the result. Any exceptions in
       * the expression are propagated (uncaught). This is useful when evaluating Angular
       * expressions.
       *
       * # Example
       * ```js
           var scope = ng.$rootScope.Scope();
           scope.a = 1;
           scope.b = 2;

           expect(scope.$eval('a+b')).toEqual(3);
           expect(scope.$eval(function(scope){ return scope.a + scope.b; })).toEqual(3);
       * ```
       *
       * @param {(string|function())=} expression An angular expression to be executed.
       *
       *    - `string`: execute using the rules as defined in  {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with the current `scope` parameter.
       *
       * @param {(object)=} locals Local variables object, useful for overriding values in scope.
       * @returns {*} The result of evaluating the expression.
       */
      $eval: function(expr, locals) {
        return $parse(expr)(this, locals);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$evalAsync
       * @kind function
       *
       * @description
       * Executes the expression on the current scope at a later point in time.
       *
       * The `$evalAsync` makes no guarantees as to when the `expression` will be executed, only
       * that:
       *
       *   - it will execute after the function that scheduled the evaluation (preferably before DOM
       *     rendering).
       *   - at least one {@link ng.$rootScope.Scope#$digest $digest cycle} will be performed after
       *     `expression` execution.
       *
       * Any exceptions from the execution of the expression are forwarded to the
       * {@link ng.$exceptionHandler $exceptionHandler} service.
       *
       * __Note:__ if this function is called outside of a `$digest` cycle, a new `$digest` cycle
       * will be scheduled. However, it is encouraged to always call code that changes the model
       * from within an `$apply` call. That includes code evaluated via `$evalAsync`.
       *
       * @param {(string|function())=} expression An angular expression to be executed.
       *
       *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with the current `scope` parameter.
       *
       * @param {(object)=} locals Local variables object, useful for overriding values in scope.
       */
      $evalAsync: function(expr, locals) {
        // if we are outside of an $digest loop and this is the first time we are scheduling async
        // task also schedule async auto-flush
        if (!$rootScope.$$phase && !asyncQueue.length) {
          $browser.defer(function() {
            if (asyncQueue.length) {
              $rootScope.$digest();
            }
          });
        }

        asyncQueue.push({scope: this, fn: $parse(expr), locals: locals});
      },

      $$postDigest: function(fn) {
        postDigestQueue.push(fn);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$apply
       * @kind function
       *
       * @description
       * `$apply()` is used to execute an expression in angular from outside of the angular
       * framework. (For example from browser DOM events, setTimeout, XHR or third party libraries).
       * Because we are calling into the angular framework we need to perform proper scope life
       * cycle of {@link ng.$exceptionHandler exception handling},
       * {@link ng.$rootScope.Scope#$digest executing watches}.
       *
       * ## Life cycle
       *
       * # Pseudo-Code of `$apply()`
       * ```js
           function $apply(expr) {
             try {
               return $eval(expr);
             } catch (e) {
               $exceptionHandler(e);
             } finally {
               $root.$digest();
             }
           }
       * ```
       *
       *
       * Scope's `$apply()` method transitions through the following stages:
       *
       * 1. The {@link guide/expression expression} is executed using the
       *    {@link ng.$rootScope.Scope#$eval $eval()} method.
       * 2. Any exceptions from the execution of the expression are forwarded to the
       *    {@link ng.$exceptionHandler $exceptionHandler} service.
       * 3. The {@link ng.$rootScope.Scope#$watch watch} listeners are fired immediately after the
       *    expression was executed using the {@link ng.$rootScope.Scope#$digest $digest()} method.
       *
       *
       * @param {(string|function())=} exp An angular expression to be executed.
       *
       *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with current `scope` parameter.
       *
       * @returns {*} The result of evaluating the expression.
       */
      $apply: function(expr) {
        try {
          beginPhase('$apply');
          try {
            return this.$eval(expr);
          } finally {
            clearPhase();
          }
        } catch (e) {
          $exceptionHandler(e);
        } finally {
          try {
            $rootScope.$digest();
          } catch (e) {
            $exceptionHandler(e);
            // eslint-disable-next-line no-unsafe-finally
            throw e;
          }
        }
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$applyAsync
       * @kind function
       *
       * @description
       * Schedule the invocation of $apply to occur at a later time. The actual time difference
       * varies across browsers, but is typically around ~10 milliseconds.
       *
       * This can be used to queue up multiple expressions which need to be evaluated in the same
       * digest.
       *
       * @param {(string|function())=} exp An angular expression to be executed.
       *
       *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with current `scope` parameter.
       */
      $applyAsync: function(expr) {
        var scope = this;
        if (expr) {
          applyAsyncQueue.push($applyAsyncExpression);
        }
        expr = $parse(expr);
        scheduleApplyAsync();

        function $applyAsyncExpression() {
          scope.$eval(expr);
        }
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$on
       * @kind function
       *
       * @description
       * Listens on events of a given type. See {@link ng.$rootScope.Scope#$emit $emit} for
       * discussion of event life cycle.
       *
       * The event listener function format is: `function(event, args...)`. The `event` object
       * passed into the listener has the following attributes:
       *
       *   - `targetScope` - `{Scope}`: the scope on which the event was `$emit`-ed or
       *     `$broadcast`-ed.
       *   - `currentScope` - `{Scope}`: the scope that is currently handling the event. Once the
       *     event propagates through the scope hierarchy, this property is set to null.
       *   - `name` - `{string}`: name of the event.
       *   - `stopPropagation` - `{function=}`: calling `stopPropagation` function will cancel
       *     further event propagation (available only for events that were `$emit`-ed).
       *   - `preventDefault` - `{function}`: calling `preventDefault` sets `defaultPrevented` flag
       *     to true.
       *   - `defaultPrevented` - `{boolean}`: true if `preventDefault` was called.
       *
       * @param {string} name Event name to listen on.
       * @param {function(event, ...args)} listener Function to call when the event is emitted.
       * @returns {function()} Returns a deregistration function for this listener.
       */
      $on: function(name, listener) {
        var namedListeners = this.$$listeners[name];
        if (!namedListeners) {
          this.$$listeners[name] = namedListeners = [];
        }
        namedListeners.push(listener);

        var current = this;
        do {
          if (!current.$$listenerCount[name]) {
            current.$$listenerCount[name] = 0;
          }
          current.$$listenerCount[name]++;
        } while ((current = current.$parent));

        var self = this;
        return function() {
          var indexOfListener = namedListeners.indexOf(listener);
          if (indexOfListener !== -1) {
            namedListeners[indexOfListener] = null;
            decrementListenerCount(self, 1, name);
          }
        };
      },


      /**
       * @ngdoc method
       * @name $rootScope.Scope#$emit
       * @kind function
       *
       * @description
       * Dispatches an event `name` upwards through the scope hierarchy notifying the
       * registered {@link ng.$rootScope.Scope#$on} listeners.
       *
       * The event life cycle starts at the scope on which `$emit` was called. All
       * {@link ng.$rootScope.Scope#$on listeners} listening for `name` event on this scope get
       * notified. Afterwards, the event traverses upwards toward the root scope and calls all
       * registered listeners along the way. The event will stop propagating if one of the listeners
       * cancels it.
       *
       * Any exception emitted from the {@link ng.$rootScope.Scope#$on listeners} will be passed
       * onto the {@link ng.$exceptionHandler $exceptionHandler} service.
       *
       * @param {string} name Event name to emit.
       * @param {...*} args Optional one or more arguments which will be passed onto the event listeners.
       * @return {Object} Event object (see {@link ng.$rootScope.Scope#$on}).
       */
      $emit: function(name, args) {
        var empty = [],
            namedListeners,
            scope = this,
            stopPropagation = false,
            event = {
              name: name,
              targetScope: scope,
              stopPropagation: function() {stopPropagation = true;},
              preventDefault: function() {
                event.defaultPrevented = true;
              },
              defaultPrevented: false
            },
            listenerArgs = concat([event], arguments, 1),
            i, length;

        do {
          namedListeners = scope.$$listeners[name] || empty;
          event.currentScope = scope;
          for (i = 0, length = namedListeners.length; i < length; i++) {

            // if listeners were deregistered, defragment the array
            if (!namedListeners[i]) {
              namedListeners.splice(i, 1);
              i--;
              length--;
              continue;
            }
            try {
              //allow all listeners attached to the current scope to run
              namedListeners[i].apply(null, listenerArgs);
            } catch (e) {
              $exceptionHandler(e);
            }
          }
          //if any listener on the current scope stops propagation, prevent bubbling
          if (stopPropagation) {
            event.currentScope = null;
            return event;
          }
          //traverse upwards
          scope = scope.$parent;
        } while (scope);

        event.currentScope = null;

        return event;
      },


      /**
       * @ngdoc method
       * @name $rootScope.Scope#$broadcast
       * @kind function
       *
       * @description
       * Dispatches an event `name` downwards to all child scopes (and their children) notifying the
       * registered {@link ng.$rootScope.Scope#$on} listeners.
       *
       * The event life cycle starts at the scope on which `$broadcast` was called. All
       * {@link ng.$rootScope.Scope#$on listeners} listening for `name` event on this scope get
       * notified. Afterwards, the event propagates to all direct and indirect scopes of the current
       * scope and calls all registered listeners along the way. The event cannot be canceled.
       *
       * Any exception emitted from the {@link ng.$rootScope.Scope#$on listeners} will be passed
       * onto the {@link ng.$exceptionHandler $exceptionHandler} service.
       *
       * @param {string} name Event name to broadcast.
       * @param {...*} args Optional one or more arguments which will be passed onto the event listeners.
       * @return {Object} Event object, see {@link ng.$rootScope.Scope#$on}
       */
      $broadcast: function(name, args) {
        var target = this,
            current = target,
            next = target,
            event = {
              name: name,
              targetScope: target,
              preventDefault: function() {
                event.defaultPrevented = true;
              },
              defaultPrevented: false
            };

        if (!target.$$listenerCount[name]) return event;

        var listenerArgs = concat([event], arguments, 1),
            listeners, i, length;

        //down while you can, then up and next sibling or up and next sibling until back at root
        while ((current = next)) {
          event.currentScope = current;
          listeners = current.$$listeners[name] || [];
          for (i = 0, length = listeners.length; i < length; i++) {
            // if listeners were deregistered, defragment the array
            if (!listeners[i]) {
              listeners.splice(i, 1);
              i--;
              length--;
              continue;
            }

            try {
              listeners[i].apply(null, listenerArgs);
            } catch (e) {
              $exceptionHandler(e);
            }
          }

          // Insanity Warning: scope depth-first traversal
          // yes, this code is a bit crazy, but it works and we have tests to prove it!
          // this piece should be kept in sync with the traversal in $digest
          // (though it differs due to having the extra check for $$listenerCount)
          if (!(next = ((current.$$listenerCount[name] && current.$$childHead) ||
              (current !== target && current.$$nextSibling)))) {
            while (current !== target && !(next = current.$$nextSibling)) {
              current = current.$parent;
            }
          }
        }

        event.currentScope = null;
        return event;
      }
    };

    var $rootScope = new Scope();

    //The internal queues. Expose them on the $rootScope for debugging/testing purposes.
    var asyncQueue = $rootScope.$$asyncQueue = [];
    var postDigestQueue = $rootScope.$$postDigestQueue = [];
    var applyAsyncQueue = $rootScope.$$applyAsyncQueue = [];

    var postDigestQueuePosition = 0;

    return $rootScope;


    function beginPhase(phase) {
      if ($rootScope.$$phase) {
        throw $rootScopeMinErr('inprog', '{0} already in progress', $rootScope.$$phase);
      }

      $rootScope.$$phase = phase;
    }

    function clearPhase() {
      $rootScope.$$phase = null;
    }

    function incrementWatchersCount(current, count) {
      do {
        current.$$watchersCount += count;
      } while ((current = current.$parent));
    }

    function decrementListenerCount(current, count, name) {
      do {
        current.$$listenerCount[name] -= count;

        if (current.$$listenerCount[name] === 0) {
          delete current.$$listenerCount[name];
        }
      } while ((current = current.$parent));
    }

    /**
     * function used as an initial value for watchers.
     * because it's unique we can easily tell it apart from other values
     */
    function initWatchVal() {}

    function flushApplyAsync() {
      while (applyAsyncQueue.length) {
        try {
          applyAsyncQueue.shift()();
        } catch (e) {
          $exceptionHandler(e);
        }
      }
      applyAsyncId = null;
    }

    function scheduleApplyAsync() {
      if (applyAsyncId === null) {
        applyAsyncId = $browser.defer(function() {
          $rootScope.$apply(flushApplyAsync);
        });
      }
    }
  }];
}

/**
 * @ngdoc service
 * @name $rootElement
 *
 * @description
 * The root element of Angular application. This is either the element where {@link
 * ng.directive:ngApp ngApp} was declared or the element passed into
 * {@link angular.bootstrap}. The element represents the root element of application. It is also the
 * location where the application's {@link auto.$injector $injector} service gets
 * published, and can be retrieved using `$rootElement.injector()`.
 */


// the implementation is in angular.bootstrap

/**
 * @this
 * @description
 * Private service to sanitize uris for links and images. Used by $compile and $sanitize.
 */
function $$SanitizeUriProvider() {
  var aHrefSanitizationWhitelist = /^\s*(https?|ftp|mailto|tel|file):/,
    imgSrcSanitizationWhitelist = /^\s*((https?|ftp|file|blob):|data:image\/)/;

  /**
   * @description
   * Retrieves or overrides the default regular expression that is used for whitelisting of safe
   * urls during a[href] sanitization.
   *
   * The sanitization is a security measure aimed at prevent XSS attacks via html links.
   *
   * Any url about to be assigned to a[href] via data-binding is first normalized and turned into
   * an absolute url. Afterwards, the url is matched against the `aHrefSanitizationWhitelist`
   * regular expression. If a match is found, the original url is written into the dom. Otherwise,
   * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
   *
   * @param {RegExp=} regexp New regexp to whitelist urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.aHrefSanitizationWhitelist = function(regexp) {
    if (isDefined(regexp)) {
      aHrefSanitizationWhitelist = regexp;
      return this;
    }
    return aHrefSanitizationWhitelist;
  };


  /**
   * @description
   * Retrieves or overrides the default regular expression that is used for whitelisting of safe
   * urls during img[src] sanitization.
   *
   * The sanitization is a security measure aimed at prevent XSS attacks via html links.
   *
   * Any url about to be assigned to img[src] via data-binding is first normalized and turned into
   * an absolute url. Afterwards, the url is matched against the `imgSrcSanitizationWhitelist`
   * regular expression. If a match is found, the original url is written into the dom. Otherwise,
   * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
   *
   * @param {RegExp=} regexp New regexp to whitelist urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.imgSrcSanitizationWhitelist = function(regexp) {
    if (isDefined(regexp)) {
      imgSrcSanitizationWhitelist = regexp;
      return this;
    }
    return imgSrcSanitizationWhitelist;
  };

  this.$get = function() {
    return function sanitizeUri(uri, isImage) {
      var regex = isImage ? imgSrcSanitizationWhitelist : aHrefSanitizationWhitelist;
      var normalizedVal;
      normalizedVal = urlResolve(uri).href;
      if (normalizedVal !== '' && !normalizedVal.match(regex)) {
        return 'unsafe:' + normalizedVal;
      }
      return uri;
    };
  };
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/* exported $SceProvider, $SceDelegateProvider */

var $sceMinErr = minErr('$sce');

var SCE_CONTEXTS = {
  HTML: 'html',
  CSS: 'css',
  URL: 'url',
  // RESOURCE_URL is a subtype of URL used in contexts where a privileged resource is sourced from a
  // url.  (e.g. ng-include, script src, templateUrl)
  RESOURCE_URL: 'resourceUrl',
  JS: 'js'
};

// Helper functions follow.

var UNDERSCORE_LOWERCASE_REGEXP = /_([a-z])/g;

function snakeToCamel(name) {
  return name
    .replace(UNDERSCORE_LOWERCASE_REGEXP, fnCamelCaseReplace);
}

function adjustMatcher(matcher) {
  if (matcher === 'self') {
    return matcher;
  } else if (isString(matcher)) {
    // Strings match exactly except for 2 wildcards - '*' and '**'.
    // '*' matches any character except those from the set ':/.?&'.
    // '**' matches any character (like .* in a RegExp).
    // More than 2 *'s raises an error as it's ill defined.
    if (matcher.indexOf('***') > -1) {
      throw $sceMinErr('iwcard',
          'Illegal sequence *** in string matcher.  String: {0}', matcher);
    }
    matcher = escapeForRegexp(matcher).
                  replace(/\\\*\\\*/g, '.*').
                  replace(/\\\*/g, '[^:/.?&;]*');
    return new RegExp('^' + matcher + '$');
  } else if (isRegExp(matcher)) {
    // The only other type of matcher allowed is a Regexp.
    // Match entire URL / disallow partial matches.
    // Flags are reset (i.e. no global, ignoreCase or multiline)
    return new RegExp('^' + matcher.source + '$');
  } else {
    throw $sceMinErr('imatcher',
        'Matchers may only be "self", string patterns or RegExp objects');
  }
}


function adjustMatchers(matchers) {
  var adjustedMatchers = [];
  if (isDefined(matchers)) {
    forEach(matchers, function(matcher) {
      adjustedMatchers.push(adjustMatcher(matcher));
    });
  }
  return adjustedMatchers;
}


/**
 * @ngdoc service
 * @name $sceDelegate
 * @kind function
 *
 * @description
 *
 * `$sceDelegate` is a service that is used by the `$sce` service to provide {@link ng.$sce Strict
 * Contextual Escaping (SCE)} services to AngularJS.
 *
 * Typically, you would configure or override the {@link ng.$sceDelegate $sceDelegate} instead of
 * the `$sce` service to customize the way Strict Contextual Escaping works in AngularJS.  This is
 * because, while the `$sce` provides numerous shorthand methods, etc., you really only need to
 * override 3 core functions (`trustAs`, `getTrusted` and `valueOf`) to replace the way things
 * work because `$sce` delegates to `$sceDelegate` for these operations.
 *
 * Refer {@link ng.$sceDelegateProvider $sceDelegateProvider} to configure this service.
 *
 * The default instance of `$sceDelegate` should work out of the box with little pain.  While you
 * can override it completely to change the behavior of `$sce`, the common case would
 * involve configuring the {@link ng.$sceDelegateProvider $sceDelegateProvider} instead by setting
 * your own whitelists and blacklists for trusting URLs used for loading AngularJS resources such as
 * templates.  Refer {@link ng.$sceDelegateProvider#resourceUrlWhitelist
 * $sceDelegateProvider.resourceUrlWhitelist} and {@link
 * ng.$sceDelegateProvider#resourceUrlBlacklist $sceDelegateProvider.resourceUrlBlacklist}
 */

/**
 * @ngdoc provider
 * @name $sceDelegateProvider
 * @this
 *
 * @description
 *
 * The `$sceDelegateProvider` provider allows developers to configure the {@link ng.$sceDelegate
 * $sceDelegate} service.  This allows one to get/set the whitelists and blacklists used to ensure
 * that the URLs used for sourcing Angular templates are safe.  Refer {@link
 * ng.$sceDelegateProvider#resourceUrlWhitelist $sceDelegateProvider.resourceUrlWhitelist} and
 * {@link ng.$sceDelegateProvider#resourceUrlBlacklist $sceDelegateProvider.resourceUrlBlacklist}
 *
 * For the general details about this service in Angular, read the main page for {@link ng.$sce
 * Strict Contextual Escaping (SCE)}.
 *
 * **Example**:  Consider the following case. <a name="example"></a>
 *
 * - your app is hosted at url `http://myapp.example.com/`
 * - but some of your templates are hosted on other domains you control such as
 *   `http://srv01.assets.example.com/`, `http://srv02.assets.example.com/`, etc.
 * - and you have an open redirect at `http://myapp.example.com/clickThru?...`.
 *
 * Here is what a secure configuration for this scenario might look like:
 *
 * ```
 *  angular.module('myApp', []).config(function($sceDelegateProvider) {
 *    $sceDelegateProvider.resourceUrlWhitelist([
 *      // Allow same origin resource loads.
 *      'self',
 *      // Allow loading from our assets domain.  Notice the difference between * and **.
 *      'http://srv*.assets.example.com/**'
 *    ]);
 *
 *    // The blacklist overrides the whitelist so the open redirect here is blocked.
 *    $sceDelegateProvider.resourceUrlBlacklist([
 *      'http://myapp.example.com/clickThru**'
 *    ]);
 *  });
 * ```
 */

function $SceDelegateProvider() {
  this.SCE_CONTEXTS = SCE_CONTEXTS;

  // Resource URLs can also be trusted by policy.
  var resourceUrlWhitelist = ['self'],
      resourceUrlBlacklist = [];

  /**
   * @ngdoc method
   * @name $sceDelegateProvider#resourceUrlWhitelist
   * @kind function
   *
   * @param {Array=} whitelist When provided, replaces the resourceUrlWhitelist with the value
   *    provided.  This must be an array or null.  A snapshot of this array is used so further
   *    changes to the array are ignored.
   *
   *    Follow {@link ng.$sce#resourceUrlPatternItem this link} for a description of the items
   *    allowed in this array.
   *
   *    <div class="alert alert-warning">
   *    **Note:** an empty whitelist array will block all URLs!
   *    </div>
   *
   * @return {Array} the currently set whitelist array.
   *
   * The **default value** when no whitelist has been explicitly set is `['self']` allowing only
   * same origin resource requests.
   *
   * @description
   * Sets/Gets the whitelist of trusted resource URLs.
   */
  this.resourceUrlWhitelist = function(value) {
    if (arguments.length) {
      resourceUrlWhitelist = adjustMatchers(value);
    }
    return resourceUrlWhitelist;
  };

  /**
   * @ngdoc method
   * @name $sceDelegateProvider#resourceUrlBlacklist
   * @kind function
   *
   * @param {Array=} blacklist When provided, replaces the resourceUrlBlacklist with the value
   *    provided.  This must be an array or null.  A snapshot of this array is used so further
   *    changes to the array are ignored.
   *
   *    Follow {@link ng.$sce#resourceUrlPatternItem this link} for a description of the items
   *    allowed in this array.
   *
   *    The typical usage for the blacklist is to **block
   *    [open redirects](http://cwe.mitre.org/data/definitions/601.html)** served by your domain as
   *    these would otherwise be trusted but actually return content from the redirected domain.
   *
   *    Finally, **the blacklist overrides the whitelist** and has the final say.
   *
   * @return {Array} the currently set blacklist array.
   *
   * The **default value** when no whitelist has been explicitly set is the empty array (i.e. there
   * is no blacklist.)
   *
   * @description
   * Sets/Gets the blacklist of trusted resource URLs.
   */

  this.resourceUrlBlacklist = function(value) {
    if (arguments.length) {
      resourceUrlBlacklist = adjustMatchers(value);
    }
    return resourceUrlBlacklist;
  };

  this.$get = ['$injector', function($injector) {

    var htmlSanitizer = function htmlSanitizer(html) {
      throw $sceMinErr('unsafe', 'Attempting to use an unsafe value in a safe context.');
    };

    if ($injector.has('$sanitize')) {
      htmlSanitizer = $injector.get('$sanitize');
    }


    function matchUrl(matcher, parsedUrl) {
      if (matcher === 'self') {
        return urlIsSameOrigin(parsedUrl);
      } else {
        // definitely a regex.  See adjustMatchers()
        return !!matcher.exec(parsedUrl.href);
      }
    }

    function isResourceUrlAllowedByPolicy(url) {
      var parsedUrl = urlResolve(url.toString());
      var i, n, allowed = false;
      // Ensure that at least one item from the whitelist allows this url.
      for (i = 0, n = resourceUrlWhitelist.length; i < n; i++) {
        if (matchUrl(resourceUrlWhitelist[i], parsedUrl)) {
          allowed = true;
          break;
        }
      }
      if (allowed) {
        // Ensure that no item from the blacklist blocked this url.
        for (i = 0, n = resourceUrlBlacklist.length; i < n; i++) {
          if (matchUrl(resourceUrlBlacklist[i], parsedUrl)) {
            allowed = false;
            break;
          }
        }
      }
      return allowed;
    }

    function generateHolderType(Base) {
      var holderType = function TrustedValueHolderType(trustedValue) {
        this.$$unwrapTrustedValue = function() {
          return trustedValue;
        };
      };
      if (Base) {
        holderType.prototype = new Base();
      }
      holderType.prototype.valueOf = function sceValueOf() {
        return this.$$unwrapTrustedValue();
      };
      holderType.prototype.toString = function sceToString() {
        return this.$$unwrapTrustedValue().toString();
      };
      return holderType;
    }

    var trustedValueHolderBase = generateHolderType(),
        byType = {};

    byType[SCE_CONTEXTS.HTML] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.CSS] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.URL] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.JS] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.RESOURCE_URL] = generateHolderType(byType[SCE_CONTEXTS.URL]);

    /**
     * @ngdoc method
     * @name $sceDelegate#trustAs
     *
     * @description
     * Returns an object that is trusted by angular for use in specified strict
     * contextual escaping contexts (such as ng-bind-html, ng-include, any src
     * attribute interpolation, any dom event binding attribute interpolation
     * such as for onclick,  etc.) that uses the provided value.
     * See {@link ng.$sce $sce} for enabling strict contextual escaping.
     *
     * @param {string} type The kind of context in which this value is safe for use.  e.g. url,
     *   resourceUrl, html, js and css.
     * @param {*} value The value that that should be considered trusted/safe.
     * @returns {*} A value that can be used to stand in for the provided `value` in places
     * where Angular expects a $sce.trustAs() return value.
     */
    function trustAs(type, trustedValue) {
      var Constructor = (byType.hasOwnProperty(type) ? byType[type] : null);
      if (!Constructor) {
        throw $sceMinErr('icontext',
            'Attempted to trust a value in invalid context. Context: {0}; Value: {1}',
            type, trustedValue);
      }
      if (trustedValue === null || isUndefined(trustedValue) || trustedValue === '') {
        return trustedValue;
      }
      // All the current contexts in SCE_CONTEXTS happen to be strings.  In order to avoid trusting
      // mutable objects, we ensure here that the value passed in is actually a string.
      if (typeof trustedValue !== 'string') {
        throw $sceMinErr('itype',
            'Attempted to trust a non-string value in a content requiring a string: Context: {0}',
            type);
      }
      return new Constructor(trustedValue);
    }

    /**
     * @ngdoc method
     * @name $sceDelegate#valueOf
     *
     * @description
     * If the passed parameter had been returned by a prior call to {@link ng.$sceDelegate#trustAs
     * `$sceDelegate.trustAs`}, returns the value that had been passed to {@link
     * ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}.
     *
     * If the passed parameter is not a value that had been returned by {@link
     * ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}, returns it as-is.
     *
     * @param {*} value The result of a prior {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}
     *      call or anything else.
     * @returns {*} The `value` that was originally provided to {@link ng.$sceDelegate#trustAs
     *     `$sceDelegate.trustAs`} if `value` is the result of such a call.  Otherwise, returns
     *     `value` unchanged.
     */
    function valueOf(maybeTrusted) {
      if (maybeTrusted instanceof trustedValueHolderBase) {
        return maybeTrusted.$$unwrapTrustedValue();
      } else {
        return maybeTrusted;
      }
    }

    /**
     * @ngdoc method
     * @name $sceDelegate#getTrusted
     *
     * @description
     * Takes the result of a {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs`} call and
     * returns the originally supplied value if the queried context type is a supertype of the
     * created type.  If this condition isn't satisfied, throws an exception.
     *
     * <div class="alert alert-danger">
     * Disabling auto-escaping is extremely dangerous, it usually creates a Cross Site Scripting
     * (XSS) vulnerability in your application.
     * </div>
     *
     * @param {string} type The kind of context in which this value is to be used.
     * @param {*} maybeTrusted The result of a prior {@link ng.$sceDelegate#trustAs
     *     `$sceDelegate.trustAs`} call.
     * @returns {*} The value the was originally provided to {@link ng.$sceDelegate#trustAs
     *     `$sceDelegate.trustAs`} if valid in this context.  Otherwise, throws an exception.
     */
    function getTrusted(type, maybeTrusted) {
      if (maybeTrusted === null || isUndefined(maybeTrusted) || maybeTrusted === '') {
        return maybeTrusted;
      }
      var constructor = (byType.hasOwnProperty(type) ? byType[type] : null);
      if (constructor && maybeTrusted instanceof constructor) {
        return maybeTrusted.$$unwrapTrustedValue();
      }
      // If we get here, then we may only take one of two actions.
      // 1. sanitize the value for the requested type, or
      // 2. throw an exception.
      if (type === SCE_CONTEXTS.RESOURCE_URL) {
        if (isResourceUrlAllowedByPolicy(maybeTrusted)) {
          return maybeTrusted;
        } else {
          throw $sceMinErr('insecurl',
              'Blocked loading resource from url not allowed by $sceDelegate policy.  URL: {0}',
              maybeTrusted.toString());
        }
      } else if (type === SCE_CONTEXTS.HTML) {
        return htmlSanitizer(maybeTrusted);
      }
      throw $sceMinErr('unsafe', 'Attempting to use an unsafe value in a safe context.');
    }

    return { trustAs: trustAs,
             getTrusted: getTrusted,
             valueOf: valueOf };
  }];
}


/**
 * @ngdoc provider
 * @name $sceProvider
 * @this
 *
 * @description
 *
 * The $sceProvider provider allows developers to configure the {@link ng.$sce $sce} service.
 * -   enable/disable Strict Contextual Escaping (SCE) in a module
 * -   override the default implementation with a custom delegate
 *
 * Read more about {@link ng.$sce Strict Contextual Escaping (SCE)}.
 */

/**
 * @ngdoc service
 * @name $sce
 * @kind function
 *
 * @description
 *
 * `$sce` is a service that provides Strict Contextual Escaping services to AngularJS.
 *
 * # Strict Contextual Escaping
 *
 * Strict Contextual Escaping (SCE) is a mode in which AngularJS requires bindings in certain
 * contexts to result in a value that is marked as safe to use for that context.  One example of
 * such a context is binding arbitrary html controlled by the user via `ng-bind-html`.  We refer
 * to these contexts as privileged or SCE contexts.
 *
 * As of version 1.2, Angular ships with SCE enabled by default.
 *
 * Note:  When enabled (the default), IE<11 in quirks mode is not supported.  In this mode, IE<11 allow
 * one to execute arbitrary javascript by the use of the expression() syntax.  Refer
 * <http://blogs.msdn.com/b/ie/archive/2008/10/16/ending-expressions.aspx> to learn more about them.
 * You can ensure your document is in standards mode and not quirks mode by adding `<!doctype html>`
 * to the top of your HTML document.
 *
 * SCE assists in writing code in a way that (a) is secure by default and (b) makes auditing for
 * security vulnerabilities such as XSS, clickjacking, etc. a lot easier.
 *
 * Here's an example of a binding in a privileged context:
 *
 * ```
 * <input ng-model="userHtml" aria-label="User input">
 * <div ng-bind-html="userHtml"></div>
 * ```
 *
 * Notice that `ng-bind-html` is bound to `userHtml` controlled by the user.  With SCE
 * disabled, this application allows the user to render arbitrary HTML into the DIV.
 * In a more realistic example, one may be rendering user comments, blog articles, etc. via
 * bindings.  (HTML is just one example of a context where rendering user controlled input creates
 * security vulnerabilities.)
 *
 * For the case of HTML, you might use a library, either on the client side, or on the server side,
 * to sanitize unsafe HTML before binding to the value and rendering it in the document.
 *
 * How would you ensure that every place that used these types of bindings was bound to a value that
 * was sanitized by your library (or returned as safe for rendering by your server?)  How can you
 * ensure that you didn't accidentally delete the line that sanitized the value, or renamed some
 * properties/fields and forgot to update the binding to the sanitized value?
 *
 * To be secure by default, you want to ensure that any such bindings are disallowed unless you can
 * determine that something explicitly says it's safe to use a value for binding in that
 * context.  You can then audit your code (a simple grep would do) to ensure that this is only done
 * for those values that you can easily tell are safe - because they were received from your server,
 * sanitized by your library, etc.  You can organize your codebase to help with this - perhaps
 * allowing only the files in a specific directory to do this.  Ensuring that the internal API
 * exposed by that code doesn't markup arbitrary values as safe then becomes a more manageable task.
 *
 * In the case of AngularJS' SCE service, one uses {@link ng.$sce#trustAs $sce.trustAs}
 * (and shorthand methods such as {@link ng.$sce#trustAsHtml $sce.trustAsHtml}, etc.) to
 * obtain values that will be accepted by SCE / privileged contexts.
 *
 *
 * ## How does it work?
 *
 * In privileged contexts, directives and code will bind to the result of {@link ng.$sce#getTrusted
 * $sce.getTrusted(context, value)} rather than to the value directly.  Directives use {@link
 * ng.$sce#parseAs $sce.parseAs} rather than `$parse` to watch attribute bindings, which performs the
 * {@link ng.$sce#getTrusted $sce.getTrusted} behind the scenes on non-constant literals.
 *
 * As an example, {@link ng.directive:ngBindHtml ngBindHtml} uses {@link
 * ng.$sce#parseAsHtml $sce.parseAsHtml(binding expression)}.  Here's the actual code (slightly
 * simplified):
 *
 * ```
 * var ngBindHtmlDirective = ['$sce', function($sce) {
 *   return function(scope, element, attr) {
 *     scope.$watch($sce.parseAsHtml(attr.ngBindHtml), function(value) {
 *       element.html(value || '');
 *     });
 *   };
 * }];
 * ```
 *
 * ## Impact on loading templates
 *
 * This applies both to the {@link ng.directive:ngInclude `ng-include`} directive as well as
 * `templateUrl`'s specified by {@link guide/directive directives}.
 *
 * By default, Angular only loads templates from the same domain and protocol as the application
 * document.  This is done by calling {@link ng.$sce#getTrustedResourceUrl
 * $sce.getTrustedResourceUrl} on the template URL.  To load templates from other domains and/or
 * protocols, you may either {@link ng.$sceDelegateProvider#resourceUrlWhitelist whitelist
 * them} or {@link ng.$sce#trustAsResourceUrl wrap it} into a trusted value.
 *
 * *Please note*:
 * The browser's
 * [Same Origin Policy](https://code.google.com/p/browsersec/wiki/Part2#Same-origin_policy_for_XMLHttpRequest)
 * and [Cross-Origin Resource Sharing (CORS)](http://www.w3.org/TR/cors/)
 * policy apply in addition to this and may further restrict whether the template is successfully
 * loaded.  This means that without the right CORS policy, loading templates from a different domain
 * won't work on all browsers.  Also, loading templates from `file://` URL does not work on some
 * browsers.
 *
 * ## This feels like too much overhead
 *
 * It's important to remember that SCE only applies to interpolation expressions.
 *
 * If your expressions are constant literals, they're automatically trusted and you don't need to
 * call `$sce.trustAs` on them (remember to include the `ngSanitize` module) (e.g.
 * `<div ng-bind-html="'<b>implicitly trusted</b>'"></div>`) just works.
 *
 * Additionally, `a[href]` and `img[src]` automatically sanitize their URLs and do not pass them
 * through {@link ng.$sce#getTrusted $sce.getTrusted}.  SCE doesn't play a role here.
 *
 * The included {@link ng.$sceDelegate $sceDelegate} comes with sane defaults to allow you to load
 * templates in `ng-include` from your application's domain without having to even know about SCE.
 * It blocks loading templates from other domains or loading templates over http from an https
 * served document.  You can change these by setting your own custom {@link
 * ng.$sceDelegateProvider#resourceUrlWhitelist whitelists} and {@link
 * ng.$sceDelegateProvider#resourceUrlBlacklist blacklists} for matching such URLs.
 *
 * This significantly reduces the overhead.  It is far easier to pay the small overhead and have an
 * application that's secure and can be audited to verify that with much more ease than bolting
 * security onto an application later.
 *
 * <a name="contexts"></a>
 * ## What trusted context types are supported?
 *
 * | Context             | Notes          |
 * |---------------------|----------------|
 * | `$sce.HTML`         | For HTML that's safe to source into the application.  The {@link ng.directive:ngBindHtml ngBindHtml} directive uses this context for bindings. If an unsafe value is encountered and the {@link ngSanitize $sanitize} module is present this will sanitize the value instead of throwing an error. |
 * | `$sce.CSS`          | For CSS that's safe to source into the application.  Currently unused.  Feel free to use it in your own directives. |
 * | `$sce.URL`          | For URLs that are safe to follow as links.  Currently unused (`<a href=` and `<img src=` sanitize their urls and don't constitute an SCE context. |
 * | `$sce.RESOURCE_URL` | For URLs that are not only safe to follow as links, but whose contents are also safe to include in your application.  Examples include `ng-include`, `src` / `ngSrc` bindings for tags other than `IMG`, `VIDEO`, `AUDIO`, `SOURCE`, and `TRACK` (e.g. `IFRAME`, `OBJECT`, etc.)  <br><br>Note that `$sce.RESOURCE_URL` makes a stronger statement about the URL than `$sce.URL` does and therefore contexts requiring values trusted for `$sce.RESOURCE_URL` can be used anywhere that values trusted for `$sce.URL` are required. |
 * | `$sce.JS`           | For JavaScript that is safe to execute in your application's context.  Currently unused.  Feel free to use it in your own directives. |
 *
 * ## Format of items in {@link ng.$sceDelegateProvider#resourceUrlWhitelist resourceUrlWhitelist}/{@link ng.$sceDelegateProvider#resourceUrlBlacklist Blacklist} <a name="resourceUrlPatternItem"></a>
 *
 *  Each element in these arrays must be one of the following:
 *
 *  - **'self'**
 *    - The special **string**, `'self'`, can be used to match against all URLs of the **same
 *      domain** as the application document using the **same protocol**.
 *  - **String** (except the special value `'self'`)
 *    - The string is matched against the full *normalized / absolute URL* of the resource
 *      being tested (substring matches are not good enough.)
 *    - There are exactly **two wildcard sequences** - `*` and `**`.  All other characters
 *      match themselves.
 *    - `*`: matches zero or more occurrences of any character other than one of the following 6
 *      characters: '`:`', '`/`', '`.`', '`?`', '`&`' and '`;`'.  It's a useful wildcard for use
 *      in a whitelist.
 *    - `**`: matches zero or more occurrences of *any* character.  As such, it's not
 *      appropriate for use in a scheme, domain, etc. as it would match too much.  (e.g.
 *      http://**.example.com/ would match http://evil.com/?ignore=.example.com/ and that might
 *      not have been the intention.)  Its usage at the very end of the path is ok.  (e.g.
 *      http://foo.example.com/templates/**).
 *  - **RegExp** (*see caveat below*)
 *    - *Caveat*:  While regular expressions are powerful and offer great flexibility,  their syntax
 *      (and all the inevitable escaping) makes them *harder to maintain*.  It's easy to
 *      accidentally introduce a bug when one updates a complex expression (imho, all regexes should
 *      have good test coverage).  For instance, the use of `.` in the regex is correct only in a
 *      small number of cases.  A `.` character in the regex used when matching the scheme or a
 *      subdomain could be matched against a `:` or literal `.` that was likely not intended.   It
 *      is highly recommended to use the string patterns and only fall back to regular expressions
 *      as a last resort.
 *    - The regular expression must be an instance of RegExp (i.e. not a string.)  It is
 *      matched against the **entire** *normalized / absolute URL* of the resource being tested
 *      (even when the RegExp did not have the `^` and `$` codes.)  In addition, any flags
 *      present on the RegExp (such as multiline, global, ignoreCase) are ignored.
 *    - If you are generating your JavaScript from some other templating engine (not
 *      recommended, e.g. in issue [#4006](https://github.com/angular/angular.js/issues/4006)),
 *      remember to escape your regular expression (and be aware that you might need more than
 *      one level of escaping depending on your templating engine and the way you interpolated
 *      the value.)  Do make use of your platform's escaping mechanism as it might be good
 *      enough before coding your own.  E.g. Ruby has
 *      [Regexp.escape(str)](http://www.ruby-doc.org/core-2.0.0/Regexp.html#method-c-escape)
 *      and Python has [re.escape](http://docs.python.org/library/re.html#re.escape).
 *      Javascript lacks a similar built in function for escaping.  Take a look at Google
 *      Closure library's [goog.string.regExpEscape(s)](
 *      http://docs.closure-library.googlecode.com/git/closure_goog_string_string.js.source.html#line962).
 *
 * Refer {@link ng.$sceDelegateProvider $sceDelegateProvider} for an example.
 *
 * ## Show me an example using SCE.
 *
 * <example module="mySceApp" deps="angular-sanitize.js" name="sce-service">
 * <file name="index.html">
 *   <div ng-controller="AppController as myCtrl">
 *     <i ng-bind-html="myCtrl.explicitlyTrustedHtml" id="explicitlyTrustedHtml"></i><br><br>
 *     <b>User comments</b><br>
 *     By default, HTML that isn't explicitly trusted (e.g. Alice's comment) is sanitized when
 *     $sanitize is available.  If $sanitize isn't available, this results in an error instead of an
 *     exploit.
 *     <div class="well">
 *       <div ng-repeat="userComment in myCtrl.userComments">
 *         <b>{{userComment.name}}</b>:
 *         <span ng-bind-html="userComment.htmlComment" class="htmlComment"></span>
 *         <br>
 *       </div>
 *     </div>
 *   </div>
 * </file>
 *
 * <file name="script.js">
 *   angular.module('mySceApp', ['ngSanitize'])
 *     .controller('AppController', ['$http', '$templateCache', '$sce',
 *       function AppController($http, $templateCache, $sce) {
 *         var self = this;
 *         $http.get('test_data.json', {cache: $templateCache}).then(function(response) {
 *           self.userComments = response.data;
 *         });
 *         self.explicitlyTrustedHtml = $sce.trustAsHtml(
 *             '<span onmouseover="this.textContent=&quot;Explicitly trusted HTML bypasses ' +
 *             'sanitization.&quot;">Hover over this text.</span>');
 *       }]);
 * </file>
 *
 * <file name="test_data.json">
 * [
 *   { "name": "Alice",
 *     "htmlComment":
 *         "<span onmouseover='this.textContent=\"PWN3D!\"'>Is <i>anyone</i> reading this?</span>"
 *   },
 *   { "name": "Bob",
 *     "htmlComment": "<i>Yes!</i>  Am I the only other one?"
 *   }
 * ]
 * </file>
 *
 * <file name="protractor.js" type="protractor">
 *   describe('SCE doc demo', function() {
 *     it('should sanitize untrusted values', function() {
 *       expect(element.all(by.css('.htmlComment')).first().getAttribute('innerHTML'))
 *           .toBe('<span>Is <i>anyone</i> reading this?</span>');
 *     });
 *
 *     it('should NOT sanitize explicitly trusted values', function() {
 *       expect(element(by.id('explicitlyTrustedHtml')).getAttribute('innerHTML')).toBe(
 *           '<span onmouseover="this.textContent=&quot;Explicitly trusted HTML bypasses ' +
 *           'sanitization.&quot;">Hover over this text.</span>');
 *     });
 *   });
 * </file>
 * </example>
 *
 *
 *
 * ## Can I disable SCE completely?
 *
 * Yes, you can.  However, this is strongly discouraged.  SCE gives you a lot of security benefits
 * for little coding overhead.  It will be much harder to take an SCE disabled application and
 * either secure it on your own or enable SCE at a later stage.  It might make sense to disable SCE
 * for cases where you have a lot of existing code that was written before SCE was introduced and
 * you're migrating them a module at a time.
 *
 * That said, here's how you can completely disable SCE:
 *
 * ```
 * angular.module('myAppWithSceDisabledmyApp', []).config(function($sceProvider) {
 *   // Completely disable SCE.  For demonstration purposes only!
 *   // Do not use in new projects.
 *   $sceProvider.enabled(false);
 * });
 * ```
 *
 */

function $SceProvider() {
  var enabled = true;

  /**
   * @ngdoc method
   * @name $sceProvider#enabled
   * @kind function
   *
   * @param {boolean=} value If provided, then enables/disables SCE.
   * @return {boolean} true if SCE is enabled, false otherwise.
   *
   * @description
   * Enables/disables SCE and returns the current value.
   */
  this.enabled = function(value) {
    if (arguments.length) {
      enabled = !!value;
    }
    return enabled;
  };


  /* Design notes on the default implementation for SCE.
   *
   * The API contract for the SCE delegate
   * -------------------------------------
   * The SCE delegate object must provide the following 3 methods:
   *
   * - trustAs(contextEnum, value)
   *     This method is used to tell the SCE service that the provided value is OK to use in the
   *     contexts specified by contextEnum.  It must return an object that will be accepted by
   *     getTrusted() for a compatible contextEnum and return this value.
   *
   * - valueOf(value)
   *     For values that were not produced by trustAs(), return them as is.  For values that were
   *     produced by trustAs(), return the corresponding input value to trustAs.  Basically, if
   *     trustAs is wrapping the given values into some type, this operation unwraps it when given
   *     such a value.
   *
   * - getTrusted(contextEnum, value)
   *     This function should return the a value that is safe to use in the context specified by
   *     contextEnum or throw and exception otherwise.
   *
   * NOTE: This contract deliberately does NOT state that values returned by trustAs() must be
   * opaque or wrapped in some holder object.  That happens to be an implementation detail.  For
   * instance, an implementation could maintain a registry of all trusted objects by context.  In
   * such a case, trustAs() would return the same object that was passed in.  getTrusted() would
   * return the same object passed in if it was found in the registry under a compatible context or
   * throw an exception otherwise.  An implementation might only wrap values some of the time based
   * on some criteria.  getTrusted() might return a value and not throw an exception for special
   * constants or objects even if not wrapped.  All such implementations fulfill this contract.
   *
   *
   * A note on the inheritance model for SCE contexts
   * ------------------------------------------------
   * I've used inheritance and made RESOURCE_URL wrapped types a subtype of URL wrapped types.  This
   * is purely an implementation details.
   *
   * The contract is simply this:
   *
   *     getTrusted($sce.RESOURCE_URL, value) succeeding implies that getTrusted($sce.URL, value)
   *     will also succeed.
   *
   * Inheritance happens to capture this in a natural way.  In some future, we
   * may not use inheritance anymore.  That is OK because no code outside of
   * sce.js and sceSpecs.js would need to be aware of this detail.
   */

  this.$get = ['$parse', '$sceDelegate', function(
                $parse,   $sceDelegate) {
    // Support: IE 9-11 only
    // Prereq: Ensure that we're not running in IE<11 quirks mode.  In that mode, IE < 11 allow
    // the "expression(javascript expression)" syntax which is insecure.
    if (enabled && msie < 8) {
      throw $sceMinErr('iequirks',
        'Strict Contextual Escaping does not support Internet Explorer version < 11 in quirks ' +
        'mode.  You can fix this by adding the text <!doctype html> to the top of your HTML ' +
        'document.  See http://docs.angularjs.org/api/ng.$sce for more information.');
    }

    var sce = shallowCopy(SCE_CONTEXTS);

    /**
     * @ngdoc method
     * @name $sce#isEnabled
     * @kind function
     *
     * @return {Boolean} true if SCE is enabled, false otherwise.  If you want to set the value, you
     * have to do it at module config time on {@link ng.$sceProvider $sceProvider}.
     *
     * @description
     * Returns a boolean indicating if SCE is enabled.
     */
    sce.isEnabled = function() {
      return enabled;
    };
    sce.trustAs = $sceDelegate.trustAs;
    sce.getTrusted = $sceDelegate.getTrusted;
    sce.valueOf = $sceDelegate.valueOf;

    if (!enabled) {
      sce.trustAs = sce.getTrusted = function(type, value) { return value; };
      sce.valueOf = identity;
    }

    /**
     * @ngdoc method
     * @name $sce#parseAs
     *
     * @description
     * Converts Angular {@link guide/expression expression} into a function.  This is like {@link
     * ng.$parse $parse} and is identical when the expression is a literal constant.  Otherwise, it
     * wraps the expression in a call to {@link ng.$sce#getTrusted $sce.getTrusted(*type*,
     * *result*)}
     *
     * @param {string} type The kind of SCE context in which this result will be used.
     * @param {string} expression String expression to compile.
     * @returns {function(context, locals)} a function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the strings
     *      are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values in
     *      `context`.
     */
    sce.parseAs = function sceParseAs(type, expr) {
      var parsed = $parse(expr);
      if (parsed.literal && parsed.constant) {
        return parsed;
      } else {
        return $parse(expr, function(value) {
          return sce.getTrusted(type, value);
        });
      }
    };

    /**
     * @ngdoc method
     * @name $sce#trustAs
     *
     * @description
     * Delegates to {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}.  As such,
     * returns an object that is trusted by angular for use in specified strict contextual
     * escaping contexts (such as ng-bind-html, ng-include, any src attribute
     * interpolation, any dom event binding attribute interpolation such as for onclick,  etc.)
     * that uses the provided value.  See * {@link ng.$sce $sce} for enabling strict contextual
     * escaping.
     *
     * @param {string} type The kind of context in which this value is safe for use.  e.g. url,
     *   resourceUrl, html, js and css.
     * @param {*} value The value that that should be considered trusted/safe.
     * @returns {*} A value that can be used to stand in for the provided `value` in places
     * where Angular expects a $sce.trustAs() return value.
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsHtml
     *
     * @description
     * Shorthand method.  `$sce.trustAsHtml(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.HTML, value)`}
     *
     * @param {*} value The value to trustAs.
     * @returns {*} An object that can be passed to {@link ng.$sce#getTrustedHtml
     *     $sce.getTrustedHtml(value)} to obtain the original value.  (privileged directives
     *     only accept expressions that are either literal constants or are the
     *     return value of {@link ng.$sce#trustAs $sce.trustAs}.)
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsUrl
     *
     * @description
     * Shorthand method.  `$sce.trustAsUrl(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.URL, value)`}
     *
     * @param {*} value The value to trustAs.
     * @returns {*} An object that can be passed to {@link ng.$sce#getTrustedUrl
     *     $sce.getTrustedUrl(value)} to obtain the original value.  (privileged directives
     *     only accept expressions that are either literal constants or are the
     *     return value of {@link ng.$sce#trustAs $sce.trustAs}.)
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsResourceUrl
     *
     * @description
     * Shorthand method.  `$sce.trustAsResourceUrl(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.RESOURCE_URL, value)`}
     *
     * @param {*} value The value to trustAs.
     * @returns {*} An object that can be passed to {@link ng.$sce#getTrustedResourceUrl
     *     $sce.getTrustedResourceUrl(value)} to obtain the original value.  (privileged directives
     *     only accept expressions that are either literal constants or are the return
     *     value of {@link ng.$sce#trustAs $sce.trustAs}.)
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsJs
     *
     * @description
     * Shorthand method.  `$sce.trustAsJs(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.JS, value)`}
     *
     * @param {*} value The value to trustAs.
     * @returns {*} An object that can be passed to {@link ng.$sce#getTrustedJs
     *     $sce.getTrustedJs(value)} to obtain the original value.  (privileged directives
     *     only accept expressions that are either literal constants or are the
     *     return value of {@link ng.$sce#trustAs $sce.trustAs}.)
     */

    /**
     * @ngdoc method
     * @name $sce#getTrusted
     *
     * @description
     * Delegates to {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted`}.  As such,
     * takes the result of a {@link ng.$sce#trustAs `$sce.trustAs`}() call and returns the
     * originally supplied value if the queried context type is a supertype of the created type.
     * If this condition isn't satisfied, throws an exception.
     *
     * @param {string} type The kind of context in which this value is to be used.
     * @param {*} maybeTrusted The result of a prior {@link ng.$sce#trustAs `$sce.trustAs`}
     *                         call.
     * @returns {*} The value the was originally provided to
     *              {@link ng.$sce#trustAs `$sce.trustAs`} if valid in this context.
     *              Otherwise, throws an exception.
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedHtml
     *
     * @description
     * Shorthand method.  `$sce.getTrustedHtml(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.HTML, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @returns {*} The return value of `$sce.getTrusted($sce.HTML, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedCss
     *
     * @description
     * Shorthand method.  `$sce.getTrustedCss(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.CSS, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @returns {*} The return value of `$sce.getTrusted($sce.CSS, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedUrl
     *
     * @description
     * Shorthand method.  `$sce.getTrustedUrl(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.URL, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @returns {*} The return value of `$sce.getTrusted($sce.URL, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedResourceUrl
     *
     * @description
     * Shorthand method.  `$sce.getTrustedResourceUrl(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.RESOURCE_URL, value)`}
     *
     * @param {*} value The value to pass to `$sceDelegate.getTrusted`.
     * @returns {*} The return value of `$sce.getTrusted($sce.RESOURCE_URL, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedJs
     *
     * @description
     * Shorthand method.  `$sce.getTrustedJs(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.JS, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @returns {*} The return value of `$sce.getTrusted($sce.JS, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsHtml
     *
     * @description
     * Shorthand method.  `$sce.parseAsHtml(expression string)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.HTML, value)`}
     *
     * @param {string} expression String expression to compile.
     * @returns {function(context, locals)} a function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the strings
     *      are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values in
     *      `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsCss
     *
     * @description
     * Shorthand method.  `$sce.parseAsCss(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.CSS, value)`}
     *
     * @param {string} expression String expression to compile.
     * @returns {function(context, locals)} a function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the strings
     *      are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values in
     *      `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsUrl
     *
     * @description
     * Shorthand method.  `$sce.parseAsUrl(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.URL, value)`}
     *
     * @param {string} expression String expression to compile.
     * @returns {function(context, locals)} a function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the strings
     *      are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values in
     *      `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsResourceUrl
     *
     * @description
     * Shorthand method.  `$sce.parseAsResourceUrl(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.RESOURCE_URL, value)`}
     *
     * @param {string} expression String expression to compile.
     * @returns {function(context, locals)} a function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the strings
     *      are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values in
     *      `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsJs
     *
     * @description
     * Shorthand method.  `$sce.parseAsJs(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.JS, value)`}
     *
     * @param {string} expression String expression to compile.
     * @returns {function(context, locals)} a function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the strings
     *      are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values in
     *      `context`.
     */

    // Shorthand delegations.
    var parse = sce.parseAs,
        getTrusted = sce.getTrusted,
        trustAs = sce.trustAs;

    forEach(SCE_CONTEXTS, function(enumValue, name) {
      var lName = lowercase(name);
      sce[snakeToCamel('parse_as_' + lName)] = function(expr) {
        return parse(enumValue, expr);
      };
      sce[snakeToCamel('get_trusted_' + lName)] = function(value) {
        return getTrusted(enumValue, value);
      };
      sce[snakeToCamel('trust_as_' + lName)] = function(value) {
        return trustAs(enumValue, value);
      };
    });

    return sce;
  }];
}

/* exported $SnifferProvider */

/**
 * !!! This is an undocumented "private" service !!!
 *
 * @name $sniffer
 * @requires $window
 * @requires $document
 * @this
 *
 * @property {boolean} history Does the browser support html5 history api ?
 * @property {boolean} transitions Does the browser support CSS transition events ?
 * @property {boolean} animations Does the browser support CSS animation events ?
 *
 * @description
 * This is very simple implementation of testing browser's features.
 */
function $SnifferProvider() {
  this.$get = ['$window', '$document', function($window, $document) {
    var eventSupport = {},
        // Chrome Packaged Apps are not allowed to access `history.pushState`.
        // If not sandboxed, they can be detected by the presence of `chrome.app.runtime`
        // (see https://developer.chrome.com/apps/api_index). If sandboxed, they can be detected by
        // the presence of an extension runtime ID and the absence of other Chrome runtime APIs
        // (see https://developer.chrome.com/apps/manifest/sandbox).
        // (NW.js apps have access to Chrome APIs, but do support `history`.)
        isNw = $window.nw && $window.nw.process,
        isChromePackagedApp =
            !isNw &&
            $window.chrome &&
            ($window.chrome.app && $window.chrome.app.runtime ||
                !$window.chrome.app && $window.chrome.runtime && $window.chrome.runtime.id),
        hasHistoryPushState = !isChromePackagedApp && $window.history && $window.history.pushState,
        android =
          toInt((/android (\d+)/.exec(lowercase(($window.navigator || {}).userAgent)) || [])[1]),
        boxee = /Boxee/i.test(($window.navigator || {}).userAgent),
        document = $document[0] || {},
        bodyStyle = document.body && document.body.style,
        transitions = false,
        animations = false;

    if (bodyStyle) {
      // Support: Android <5, Blackberry Browser 10, default Chrome in Android 4.4.x
      // Mentioned browsers need a -webkit- prefix for transitions & animations.
      transitions = !!('transition' in bodyStyle || 'webkitTransition' in bodyStyle);
      animations = !!('animation' in bodyStyle || 'webkitAnimation' in bodyStyle);
    }


    return {
      // Android has history.pushState, but it does not update location correctly
      // so let's not use the history API at all.
      // http://code.google.com/p/android/issues/detail?id=17471
      // https://github.com/angular/angular.js/issues/904

      // older webkit browser (533.9) on Boxee box has exactly the same problem as Android has
      // so let's not use the history API also
      // We are purposefully using `!(android < 4)` to cover the case when `android` is undefined
      history: !!(hasHistoryPushState && !(android < 4) && !boxee),
      hasEvent: function(event) {
        // Support: IE 9-11 only
        // IE9 implements 'input' event it's so fubared that we rather pretend that it doesn't have
        // it. In particular the event is not fired when backspace or delete key are pressed or
        // when cut operation is performed.
        // IE10+ implements 'input' event but it erroneously fires under various situations,
        // e.g. when placeholder changes, or a form is focused.
        if (event === 'input' && msie) return false;

        if (isUndefined(eventSupport[event])) {
          var divElm = document.createElement('div');
          eventSupport[event] = 'on' + event in divElm;
        }

        return eventSupport[event];
      },
      csp: csp(),
      transitions: transitions,
      animations: animations,
      android: android
    };
  }];
}

var $templateRequestMinErr = minErr('$compile');

/**
 * @ngdoc provider
 * @name $templateRequestProvider
 * @this
 *
 * @description
 * Used to configure the options passed to the {@link $http} service when making a template request.
 *
 * For example, it can be used for specifying the "Accept" header that is sent to the server, when
 * requesting a template.
 */
function $TemplateRequestProvider() {

  var httpOptions;

  /**
   * @ngdoc method
   * @name $templateRequestProvider#httpOptions
   * @description
   * The options to be passed to the {@link $http} service when making the request.
   * You can use this to override options such as the "Accept" header for template requests.
   *
   * The {@link $templateRequest} will set the `cache` and the `transformResponse` properties of the
   * options if not overridden here.
   *
   * @param {string=} value new value for the {@link $http} options.
   * @returns {string|self} Returns the {@link $http} options when used as getter and self if used as setter.
   */
  this.httpOptions = function(val) {
    if (val) {
      httpOptions = val;
      return this;
    }
    return httpOptions;
  };

  /**
   * @ngdoc service
   * @name $templateRequest
   *
   * @description
   * The `$templateRequest` service runs security checks then downloads the provided template using
   * `$http` and, upon success, stores the contents inside of `$templateCache`. If the HTTP request
   * fails or the response data of the HTTP request is empty, a `$compile` error will be thrown (the
   * exception can be thwarted by setting the 2nd parameter of the function to true). Note that the
   * contents of `$templateCache` are trusted, so the call to `$sce.getTrustedUrl(tpl)` is omitted
   * when `tpl` is of type string and `$templateCache` has the matching entry.
   *
   * If you want to pass custom options to the `$http` service, such as setting the Accept header you
   * can configure this via {@link $templateRequestProvider#httpOptions}.
   *
   * @param {string|TrustedResourceUrl} tpl The HTTP request template URL
   * @param {boolean=} ignoreRequestError Whether or not to ignore the exception when the request fails or the template is empty
   *
   * @return {Promise} a promise for the HTTP response data of the given URL.
   *
   * @property {number} totalPendingRequests total amount of pending template requests being downloaded.
   */
  this.$get = ['$exceptionHandler', '$templateCache', '$http', '$q', '$sce',
    function($exceptionHandler, $templateCache, $http, $q, $sce) {

      function handleRequestFn(tpl, ignoreRequestError) {
        handleRequestFn.totalPendingRequests++;

        // We consider the template cache holds only trusted templates, so
        // there's no need to go through whitelisting again for keys that already
        // are included in there. This also makes Angular accept any script
        // directive, no matter its name. However, we still need to unwrap trusted
        // types.
        if (!isString(tpl) || isUndefined($templateCache.get(tpl))) {
          tpl = $sce.getTrustedResourceUrl(tpl);
        }

        var transformResponse = $http.defaults && $http.defaults.transformResponse;

        if (isArray(transformResponse)) {
          transformResponse = transformResponse.filter(function(transformer) {
            return transformer !== defaultHttpResponseTransform;
          });
        } else if (transformResponse === defaultHttpResponseTransform) {
          transformResponse = null;
        }

        return $http.get(tpl, extend({
            cache: $templateCache,
            transformResponse: transformResponse
          }, httpOptions))
          .finally(function() {
            handleRequestFn.totalPendingRequests--;
          })
          .then(function(response) {
            $templateCache.put(tpl, response.data);
            return response.data;
          }, handleError);

        function handleError(resp) {
          if (!ignoreRequestError) {
            resp = $templateRequestMinErr('tpload',
                'Failed to load template: {0} (HTTP status: {1} {2})',
                tpl, resp.status, resp.statusText);

            $exceptionHandler(resp);
          }

          return $q.reject(resp);
        }
      }

      handleRequestFn.totalPendingRequests = 0;

      return handleRequestFn;
    }
  ];
}

/** @this */
function $$TestabilityProvider() {
  this.$get = ['$rootScope', '$browser', '$location',
       function($rootScope,   $browser,   $location) {

    /**
     * @name $testability
     *
     * @description
     * The private $$testability service provides a collection of methods for use when debugging
     * or by automated test and debugging tools.
     */
    var testability = {};

    /**
     * @name $$testability#findBindings
     *
     * @description
     * Returns an array of elements that are bound (via ng-bind or {{}})
     * to expressions matching the input.
     *
     * @param {Element} element The element root to search from.
     * @param {string} expression The binding expression to match.
     * @param {boolean} opt_exactMatch If true, only returns exact matches
     *     for the expression. Filters and whitespace are ignored.
     */
    testability.findBindings = function(element, expression, opt_exactMatch) {
      var bindings = element.getElementsByClassName('ng-binding');
      var matches = [];
      forEach(bindings, function(binding) {
        var dataBinding = angular.element(binding).data('$binding');
        if (dataBinding) {
          forEach(dataBinding, function(bindingName) {
            if (opt_exactMatch) {
              var matcher = new RegExp('(^|\\s)' + escapeForRegexp(expression) + '(\\s|\\||$)');
              if (matcher.test(bindingName)) {
                matches.push(binding);
              }
            } else {
              if (bindingName.indexOf(expression) !== -1) {
                matches.push(binding);
              }
            }
          });
        }
      });
      return matches;
    };

    /**
     * @name $$testability#findModels
     *
     * @description
     * Returns an array of elements that are two-way found via ng-model to
     * expressions matching the input.
     *
     * @param {Element} element The element root to search from.
     * @param {string} expression The model expression to match.
     * @param {boolean} opt_exactMatch If true, only returns exact matches
     *     for the expression.
     */
    testability.findModels = function(element, expression, opt_exactMatch) {
      var prefixes = ['ng-', 'data-ng-', 'ng\\:'];
      for (var p = 0; p < prefixes.length; ++p) {
        var attributeEquals = opt_exactMatch ? '=' : '*=';
        var selector = '[' + prefixes[p] + 'model' + attributeEquals + '"' + expression + '"]';
        var elements = element.querySelectorAll(selector);
        if (elements.length) {
          return elements;
        }
      }
    };

    /**
     * @name $$testability#getLocation
     *
     * @description
     * Shortcut for getting the location in a browser agnostic way. Returns
     *     the path, search, and hash. (e.g. /path?a=b#hash)
     */
    testability.getLocation = function() {
      return $location.url();
    };

    /**
     * @name $$testability#setLocation
     *
     * @description
     * Shortcut for navigating to a location without doing a full page reload.
     *
     * @param {string} url The location url (path, search and hash,
     *     e.g. /path?a=b#hash) to go to.
     */
    testability.setLocation = function(url) {
      if (url !== $location.url()) {
        $location.url(url);
        $rootScope.$digest();
      }
    };

    /**
     * @name $$testability#whenStable
     *
     * @description
     * Calls the callback when $timeout and $http requests are completed.
     *
     * @param {function} callback
     */
    testability.whenStable = function(callback) {
      $browser.notifyWhenNoOutstandingRequests(callback);
    };

    return testability;
  }];
}

/** @this */
function $TimeoutProvider() {
  this.$get = ['$rootScope', '$browser', '$q', '$$q', '$exceptionHandler',
       function($rootScope,   $browser,   $q,   $$q,   $exceptionHandler) {

    var deferreds = {};


     /**
      * @ngdoc service
      * @name $timeout
      *
      * @description
      * Angular's wrapper for `window.setTimeout`. The `fn` function is wrapped into a try/catch
      * block and delegates any exceptions to
      * {@link ng.$exceptionHandler $exceptionHandler} service.
      *
      * The return value of calling `$timeout` is a promise, which will be resolved when
      * the delay has passed and the timeout function, if provided, is executed.
      *
      * To cancel a timeout request, call `$timeout.cancel(promise)`.
      *
      * In tests you can use {@link ngMock.$timeout `$timeout.flush()`} to
      * synchronously flush the queue of deferred functions.
      *
      * If you only want a promise that will be resolved after some specified delay
      * then you can call `$timeout` without the `fn` function.
      *
      * @param {function()=} fn A function, whose execution should be delayed.
      * @param {number=} [delay=0] Delay in milliseconds.
      * @param {boolean=} [invokeApply=true] If set to `false` skips model dirty checking, otherwise
      *   will invoke `fn` within the {@link ng.$rootScope.Scope#$apply $apply} block.
      * @param {...*=} Pass additional parameters to the executed function.
      * @returns {Promise} Promise that will be resolved when the timeout is reached. The promise
      *   will be resolved with the return value of the `fn` function.
      *
      */
    function timeout(fn, delay, invokeApply) {
      if (!isFunction(fn)) {
        invokeApply = delay;
        delay = fn;
        fn = noop;
      }

      var args = sliceArgs(arguments, 3),
          skipApply = (isDefined(invokeApply) && !invokeApply),
          deferred = (skipApply ? $$q : $q).defer(),
          promise = deferred.promise,
          timeoutId;

      timeoutId = $browser.defer(function() {
        try {
          deferred.resolve(fn.apply(null, args));
        } catch (e) {
          deferred.reject(e);
          $exceptionHandler(e);
        } finally {
          delete deferreds[promise.$$timeoutId];
        }

        if (!skipApply) $rootScope.$apply();
      }, delay);

      promise.$$timeoutId = timeoutId;
      deferreds[timeoutId] = deferred;

      return promise;
    }


     /**
      * @ngdoc method
      * @name $timeout#cancel
      *
      * @description
      * Cancels a task associated with the `promise`. As a result of this, the promise will be
      * resolved with a rejection.
      *
      * @param {Promise=} promise Promise returned by the `$timeout` function.
      * @returns {boolean} Returns `true` if the task hasn't executed yet and was successfully
      *   canceled.
      */
    timeout.cancel = function(promise) {
      if (promise && promise.$$timeoutId in deferreds) {
        // Timeout cancels should not report an unhandled promise.
        deferreds[promise.$$timeoutId].promise.catch(noop);
        deferreds[promise.$$timeoutId].reject('canceled');
        delete deferreds[promise.$$timeoutId];
        return $browser.defer.cancel(promise.$$timeoutId);
      }
      return false;
    };

    return timeout;
  }];
}

// NOTE:  The usage of window and document instead of $window and $document here is
// deliberate.  This service depends on the specific behavior of anchor nodes created by the
// browser (resolving and parsing URLs) that is unlikely to be provided by mock objects and
// cause us to break tests.  In addition, when the browser resolves a URL for XHR, it
// doesn't know about mocked locations and resolves URLs to the real document - which is
// exactly the behavior needed here.  There is little value is mocking these out for this
// service.
var urlParsingNode = window.document.createElement('a');
var originUrl = urlResolve(window.location.href);


/**
 *
 * Implementation Notes for non-IE browsers
 * ----------------------------------------
 * Assigning a URL to the href property of an anchor DOM node, even one attached to the DOM,
 * results both in the normalizing and parsing of the URL.  Normalizing means that a relative
 * URL will be resolved into an absolute URL in the context of the application document.
 * Parsing means that the anchor node's host, hostname, protocol, port, pathname and related
 * properties are all populated to reflect the normalized URL.  This approach has wide
 * compatibility - Safari 1+, Mozilla 1+ etc.  See
 * http://www.aptana.com/reference/html/api/HTMLAnchorElement.html
 *
 * Implementation Notes for IE
 * ---------------------------
 * IE <= 10 normalizes the URL when assigned to the anchor node similar to the other
 * browsers.  However, the parsed components will not be set if the URL assigned did not specify
 * them.  (e.g. if you assign a.href = "foo", then a.protocol, a.host, etc. will be empty.)  We
 * work around that by performing the parsing in a 2nd step by taking a previously normalized
 * URL (e.g. by assigning to a.href) and assigning it a.href again.  This correctly populates the
 * properties such as protocol, hostname, port, etc.
 *
 * References:
 *   http://developer.mozilla.org/en-US/docs/Web/API/HTMLAnchorElement
 *   http://www.aptana.com/reference/html/api/HTMLAnchorElement.html
 *   http://url.spec.whatwg.org/#urlutils
 *   https://github.com/angular/angular.js/pull/2902
 *   http://james.padolsey.com/javascript/parsing-urls-with-the-dom/
 *
 * @kind function
 * @param {string} url The URL to be parsed.
 * @description Normalizes and parses a URL.
 * @returns {object} Returns the normalized URL as a dictionary.
 *
 *   | member name   | Description    |
 *   |---------------|----------------|
 *   | href          | A normalized version of the provided URL if it was not an absolute URL |
 *   | protocol      | The protocol including the trailing colon                              |
 *   | host          | The host and port (if the port is non-default) of the normalizedUrl    |
 *   | search        | The search params, minus the question mark                             |
 *   | hash          | The hash string, minus the hash symbol
 *   | hostname      | The hostname
 *   | port          | The port, without ":"
 *   | pathname      | The pathname, beginning with "/"
 *
 */
function urlResolve(url) {
  var href = url;

  // Support: IE 9-11 only
  if (msie) {
    // Normalize before parse.  Refer Implementation Notes on why this is
    // done in two steps on IE.
    urlParsingNode.setAttribute('href', href);
    href = urlParsingNode.href;
  }

  urlParsingNode.setAttribute('href', href);

  // urlParsingNode provides the UrlUtils interface - http://url.spec.whatwg.org/#urlutils
  return {
    href: urlParsingNode.href,
    protocol: urlParsingNode.protocol ? urlParsingNode.protocol.replace(/:$/, '') : '',
    host: urlParsingNode.host,
    search: urlParsingNode.search ? urlParsingNode.search.replace(/^\?/, '') : '',
    hash: urlParsingNode.hash ? urlParsingNode.hash.replace(/^#/, '') : '',
    hostname: urlParsingNode.hostname,
    port: urlParsingNode.port,
    pathname: (urlParsingNode.pathname.charAt(0) === '/')
      ? urlParsingNode.pathname
      : '/' + urlParsingNode.pathname
  };
}

/**
 * Parse a request URL and determine whether this is a same-origin request as the application document.
 *
 * @param {string|object} requestUrl The url of the request as a string that will be resolved
 * or a parsed URL object.
 * @returns {boolean} Whether the request is for the same origin as the application document.
 */
function urlIsSameOrigin(requestUrl) {
  var parsed = (isString(requestUrl)) ? urlResolve(requestUrl) : requestUrl;
  return (parsed.protocol === originUrl.protocol &&
          parsed.host === originUrl.host);
}

/**
 * @ngdoc service
 * @name $window
 * @this
 *
 * @description
 * A reference to the browser's `window` object. While `window`
 * is globally available in JavaScript, it causes testability problems, because
 * it is a global variable. In angular we always refer to it through the
 * `$window` service, so it may be overridden, removed or mocked for testing.
 *
 * Expressions, like the one defined for the `ngClick` directive in the example
 * below, are evaluated with respect to the current scope.  Therefore, there is
 * no risk of inadvertently coding in a dependency on a global value in such an
 * expression.
 *
 * @example
   <example module="windowExample" name="window-service">
     <file name="index.html">
       <script>
         angular.module('windowExample', [])
           .controller('ExampleController', ['$scope', '$window', function($scope, $window) {
             $scope.greeting = 'Hello, World!';
             $scope.doGreeting = function(greeting) {
               $window.alert(greeting);
             };
           }]);
       </script>
       <div ng-controller="ExampleController">
         <input type="text" ng-model="greeting" aria-label="greeting" />
         <button ng-click="doGreeting(greeting)">ALERT</button>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
      it('should display the greeting in the input box', function() {
       element(by.model('greeting')).sendKeys('Hello, E2E Tests');
       // If we click the button it will block the test runner
       // element(':button').click();
      });
     </file>
   </example>
 */
function $WindowProvider() {
  this.$get = valueFn(window);
}

/**
 * @name $$cookieReader
 * @requires $document
 *
 * @description
 * This is a private service for reading cookies used by $http and ngCookies
 *
 * @return {Object} a key/value map of the current cookies
 */
function $$CookieReader($document) {
  var rawDocument = $document[0] || {};
  var lastCookies = {};
  var lastCookieString = '';

  function safeGetCookie(rawDocument) {
    try {
      return rawDocument.cookie || '';
    } catch (e) {
      return '';
    }
  }

  function safeDecodeURIComponent(str) {
    try {
      return decodeURIComponent(str);
    } catch (e) {
      return str;
    }
  }

  return function() {
    var cookieArray, cookie, i, index, name;
    var currentCookieString = safeGetCookie(rawDocument);

    if (currentCookieString !== lastCookieString) {
      lastCookieString = currentCookieString;
      cookieArray = lastCookieString.split('; ');
      lastCookies = {};

      for (i = 0; i < cookieArray.length; i++) {
        cookie = cookieArray[i];
        index = cookie.indexOf('=');
        if (index > 0) { //ignore nameless cookies
          name = safeDecodeURIComponent(cookie.substring(0, index));
          // the first value that is seen for a cookie is the most
          // specific one.  values for the same cookie name that
          // follow are for less specific paths.
          if (isUndefined(lastCookies[name])) {
            lastCookies[name] = safeDecodeURIComponent(cookie.substring(index + 1));
          }
        }
      }
    }
    return lastCookies;
  };
}

$$CookieReader.$inject = ['$document'];

/** @this */
function $$CookieReaderProvider() {
  this.$get = $$CookieReader;
}

/* global currencyFilter: true,
 dateFilter: true,
 filterFilter: true,
 jsonFilter: true,
 limitToFilter: true,
 lowercaseFilter: true,
 numberFilter: true,
 orderByFilter: true,
 uppercaseFilter: true,
 */

/**
 * @ngdoc provider
 * @name $filterProvider
 * @description
 *
 * Filters are just functions which transform input to an output. However filters need to be
 * Dependency Injected. To achieve this a filter definition consists of a factory function which is
 * annotated with dependencies and is responsible for creating a filter function.
 *
 * <div class="alert alert-warning">
 * **Note:** Filter names must be valid angular {@link expression} identifiers, such as `uppercase` or `orderBy`.
 * Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
 * your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
 * (`myapp_subsection_filterx`).
 * </div>
 *
 * ```js
 *   // Filter registration
 *   function MyModule($provide, $filterProvider) {
 *     // create a service to demonstrate injection (not always needed)
 *     $provide.value('greet', function(name){
 *       return 'Hello ' + name + '!';
 *     });
 *
 *     // register a filter factory which uses the
 *     // greet service to demonstrate DI.
 *     $filterProvider.register('greet', function(greet){
 *       // return the filter function which uses the greet service
 *       // to generate salutation
 *       return function(text) {
 *         // filters need to be forgiving so check input validity
 *         return text && greet(text) || text;
 *       };
 *     });
 *   }
 * ```
 *
 * The filter function is registered with the `$injector` under the filter name suffix with
 * `Filter`.
 *
 * ```js
 *   it('should be the same instance', inject(
 *     function($filterProvider) {
 *       $filterProvider.register('reverse', function(){
 *         return ...;
 *       });
 *     },
 *     function($filter, reverseFilter) {
 *       expect($filter('reverse')).toBe(reverseFilter);
 *     });
 * ```
 *
 *
 * For more information about how angular filters work, and how to create your own filters, see
 * {@link guide/filter Filters} in the Angular Developer Guide.
 */

/**
 * @ngdoc service
 * @name $filter
 * @kind function
 * @description
 * Filters are used for formatting data displayed to the user.
 *
 * They can be used in view templates, controllers or services.Angular comes
 * with a collection of [built-in filters](api/ng/filter), but it is easy to
 * define your own as well.
 *
 * The general syntax in templates is as follows:
 *
 * ```html
 * {{ expression [| filter_name[:parameter_value] ... ] }}
 * ```
 *
 * @param {String} name Name of the filter function to retrieve
 * @return {Function} the filter function
 * @example
   <example name="$filter" module="filterExample">
     <file name="index.html">
       <div ng-controller="MainCtrl">
        <h3>{{ originalText }}</h3>
        <h3>{{ filteredText }}</h3>
       </div>
     </file>

     <file name="script.js">
      angular.module('filterExample', [])
      .controller('MainCtrl', function($scope, $filter) {
        $scope.originalText = 'hello';
        $scope.filteredText = $filter('uppercase')($scope.originalText);
      });
     </file>
   </example>
  */
$FilterProvider.$inject = ['$provide'];
/** @this */
function $FilterProvider($provide) {
  var suffix = 'Filter';

  /**
   * @ngdoc method
   * @name $filterProvider#register
   * @param {string|Object} name Name of the filter function, or an object map of filters where
   *    the keys are the filter names and the values are the filter factories.
   *
   *    <div class="alert alert-warning">
   *    **Note:** Filter names must be valid angular {@link expression} identifiers, such as `uppercase` or `orderBy`.
   *    Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
   *    your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
   *    (`myapp_subsection_filterx`).
   *    </div>
    * @param {Function} factory If the first argument was a string, a factory function for the filter to be registered.
   * @returns {Object} Registered filter instance, or if a map of filters was provided then a map
   *    of the registered filter instances.
   */
  function register(name, factory) {
    if (isObject(name)) {
      var filters = {};
      forEach(name, function(filter, key) {
        filters[key] = register(key, filter);
      });
      return filters;
    } else {
      return $provide.factory(name + suffix, factory);
    }
  }
  this.register = register;

  this.$get = ['$injector', function($injector) {
    return function(name) {
      return $injector.get(name + suffix);
    };
  }];

  ////////////////////////////////////////

  /* global
    currencyFilter: false,
    dateFilter: false,
    filterFilter: false,
    jsonFilter: false,
    limitToFilter: false,
    lowercaseFilter: false,
    numberFilter: false,
    orderByFilter: false,
    uppercaseFilter: false
  */

  register('currency', currencyFilter);
  register('date', dateFilter);
  register('filter', filterFilter);
  register('json', jsonFilter);
  register('limitTo', limitToFilter);
  register('lowercase', lowercaseFilter);
  register('number', numberFilter);
  register('orderBy', orderByFilter);
  register('uppercase', uppercaseFilter);
}

/**
 * @ngdoc filter
 * @name filter
 * @kind function
 *
 * @description
 * Selects a subset of items from `array` and returns it as a new array.
 *
 * @param {Array} array The source array.
 * <div class="alert alert-info">
 *   **Note**: If the array contains objects that reference themselves, filtering is not possible.
 * </div>
 * @param {string|Object|function()} expression The predicate to be used for selecting items from
 *   `array`.
 *
 *   Can be one of:
 *
 *   - `string`: The string is used for matching against the contents of the `array`. All strings or
 *     objects with string properties in `array` that match this string will be returned. This also
 *     applies to nested object properties.
 *     The predicate can be negated by prefixing the string with `!`.
 *
 *   - `Object`: A pattern object can be used to filter specific properties on objects contained
 *     by `array`. For example `{name:"M", phone:"1"}` predicate will return an array of items
 *     which have property `name` containing "M" and property `phone` containing "1". A special
 *     property name (`$` by default) can be used (e.g. as in `{$: "text"}`) to accept a match
 *     against any property of the object or its nested object properties. That's equivalent to the
 *     simple substring match with a `string` as described above. The special property name can be
 *     overwritten, using the `anyPropertyKey` parameter.
 *     The predicate can be negated by prefixing the string with `!`.
 *     For example `{name: "!M"}` predicate will return an array of items which have property `name`
 *     not containing "M".
 *
 *     Note that a named property will match properties on the same level only, while the special
 *     `$` property will match properties on the same level or deeper. E.g. an array item like
 *     `{name: {first: 'John', last: 'Doe'}}` will **not** be matched by `{name: 'John'}`, but
 *     **will** be matched by `{$: 'John'}`.
 *
 *   - `function(value, index, array)`: A predicate function can be used to write arbitrary filters.
 *     The function is called for each element of the array, with the element, its index, and
 *     the entire array itself as arguments.
 *
 *     The final result is an array of those elements that the predicate returned true for.
 *
 * @param {function(actual, expected)|true|false} [comparator] Comparator which is used in
 *     determining if the expected value (from the filter expression) and actual value (from
 *     the object in the array) should be considered a match.
 *
 *   Can be one of:
 *
 *   - `function(actual, expected)`:
 *     The function will be given the object value and the predicate value to compare and
 *     should return true if both values should be considered equal.
 *
 *   - `true`: A shorthand for `function(actual, expected) { return angular.equals(actual, expected)}`.
 *     This is essentially strict comparison of expected and actual.
 *
 *   - `false`: A short hand for a function which will look for a substring match in a case
 *     insensitive way. Primitive values are converted to strings. Objects are not compared against
 *     primitives, unless they have a custom `toString` method (e.g. `Date` objects).
 *
 *
 *   Defaults to `false`.
 *
 * @param {string} [anyPropertyKey] The special property name that matches against any property.
 *     By default `$`.
 *
 * @example
   <example name="filter-filter">
     <file name="index.html">
       <div ng-init="friends = [{name:'John', phone:'555-1276'},
                                {name:'Mary', phone:'800-BIG-MARY'},
                                {name:'Mike', phone:'555-4321'},
                                {name:'Adam', phone:'555-5678'},
                                {name:'Julie', phone:'555-8765'},
                                {name:'Juliette', phone:'555-5678'}]"></div>

       <label>Search: <input ng-model="searchText"></label>
       <table id="searchTextResults">
         <tr><th>Name</th><th>Phone</th></tr>
         <tr ng-repeat="friend in friends | filter:searchText">
           <td>{{friend.name}}</td>
           <td>{{friend.phone}}</td>
         </tr>
       </table>
       <hr>
       <label>Any: <input ng-model="search.$"></label> <br>
       <label>Name only <input ng-model="search.name"></label><br>
       <label>Phone only <input ng-model="search.phone"></label><br>
       <label>Equality <input type="checkbox" ng-model="strict"></label><br>
       <table id="searchObjResults">
         <tr><th>Name</th><th>Phone</th></tr>
         <tr ng-repeat="friendObj in friends | filter:search:strict">
           <td>{{friendObj.name}}</td>
           <td>{{friendObj.phone}}</td>
         </tr>
       </table>
     </file>
     <file name="protractor.js" type="protractor">
       var expectFriendNames = function(expectedNames, key) {
         element.all(by.repeater(key + ' in friends').column(key + '.name')).then(function(arr) {
           arr.forEach(function(wd, i) {
             expect(wd.getText()).toMatch(expectedNames[i]);
           });
         });
       };

       it('should search across all fields when filtering with a string', function() {
         var searchText = element(by.model('searchText'));
         searchText.clear();
         searchText.sendKeys('m');
         expectFriendNames(['Mary', 'Mike', 'Adam'], 'friend');

         searchText.clear();
         searchText.sendKeys('76');
         expectFriendNames(['John', 'Julie'], 'friend');
       });

       it('should search in specific fields when filtering with a predicate object', function() {
         var searchAny = element(by.model('search.$'));
         searchAny.clear();
         searchAny.sendKeys('i');
         expectFriendNames(['Mary', 'Mike', 'Julie', 'Juliette'], 'friendObj');
       });
       it('should use a equal comparison when comparator is true', function() {
         var searchName = element(by.model('search.name'));
         var strict = element(by.model('strict'));
         searchName.clear();
         searchName.sendKeys('Julie');
         strict.click();
         expectFriendNames(['Julie'], 'friendObj');
       });
     </file>
   </example>
 */

function filterFilter() {
  return function(array, expression, comparator, anyPropertyKey) {
    if (!isArrayLike(array)) {
      if (array == null) {
        return array;
      } else {
        throw minErr('filter')('notarray', 'Expected array but received: {0}', array);
      }
    }

    anyPropertyKey = anyPropertyKey || '$';
    var expressionType = getTypeForFilter(expression);
    var predicateFn;
    var matchAgainstAnyProp;

    switch (expressionType) {
      case 'function':
        predicateFn = expression;
        break;
      case 'boolean':
      case 'null':
      case 'number':
      case 'string':
        matchAgainstAnyProp = true;
        // falls through
      case 'object':
        predicateFn = createPredicateFn(expression, comparator, anyPropertyKey, matchAgainstAnyProp);
        break;
      default:
        return array;
    }

    return Array.prototype.filter.call(array, predicateFn);
  };
}

// Helper functions for `filterFilter`
function createPredicateFn(expression, comparator, anyPropertyKey, matchAgainstAnyProp) {
  var shouldMatchPrimitives = isObject(expression) && (anyPropertyKey in expression);
  var predicateFn;

  if (comparator === true) {
    comparator = equals;
  } else if (!isFunction(comparator)) {
    comparator = function(actual, expected) {
      if (isUndefined(actual)) {
        // No substring matching against `undefined`
        return false;
      }
      if ((actual === null) || (expected === null)) {
        // No substring matching against `null`; only match against `null`
        return actual === expected;
      }
      if (isObject(expected) || (isObject(actual) && !hasCustomToString(actual))) {
        // Should not compare primitives against objects, unless they have custom `toString` method
        return false;
      }

      actual = lowercase('' + actual);
      expected = lowercase('' + expected);
      return actual.indexOf(expected) !== -1;
    };
  }

  predicateFn = function(item) {
    if (shouldMatchPrimitives && !isObject(item)) {
      return deepCompare(item, expression[anyPropertyKey], comparator, anyPropertyKey, false);
    }
    return deepCompare(item, expression, comparator, anyPropertyKey, matchAgainstAnyProp);
  };

  return predicateFn;
}

function deepCompare(actual, expected, comparator, anyPropertyKey, matchAgainstAnyProp, dontMatchWholeObject) {
  var actualType = getTypeForFilter(actual);
  var expectedType = getTypeForFilter(expected);

  if ((expectedType === 'string') && (expected.charAt(0) === '!')) {
    return !deepCompare(actual, expected.substring(1), comparator, anyPropertyKey, matchAgainstAnyProp);
  } else if (isArray(actual)) {
    // In case `actual` is an array, consider it a match
    // if ANY of it's items matches `expected`
    return actual.some(function(item) {
      return deepCompare(item, expected, comparator, anyPropertyKey, matchAgainstAnyProp);
    });
  }

  switch (actualType) {
    case 'object':
      var key;
      if (matchAgainstAnyProp) {
        for (key in actual) {
          // Under certain, rare, circumstances, key may not be a string and `charAt` will be undefined
          // See: https://github.com/angular/angular.js/issues/15644
          if (key.charAt && (key.charAt(0) !== '$') &&
              deepCompare(actual[key], expected, comparator, anyPropertyKey, true)) {
            return true;
          }
        }
        return dontMatchWholeObject ? false : deepCompare(actual, expected, comparator, anyPropertyKey, false);
      } else if (expectedType === 'object') {
        for (key in expected) {
          var expectedVal = expected[key];
          if (isFunction(expectedVal) || isUndefined(expectedVal)) {
            continue;
          }

          var matchAnyProperty = key === anyPropertyKey;
          var actualVal = matchAnyProperty ? actual : actual[key];
          if (!deepCompare(actualVal, expectedVal, comparator, anyPropertyKey, matchAnyProperty, matchAnyProperty)) {
            return false;
          }
        }
        return true;
      } else {
        return comparator(actual, expected);
      }
    case 'function':
      return false;
    default:
      return comparator(actual, expected);
  }
}

// Used for easily differentiating between `null` and actual `object`
function getTypeForFilter(val) {
  return (val === null) ? 'null' : typeof val;
}

var MAX_DIGITS = 22;
var DECIMAL_SEP = '.';
var ZERO_CHAR = '0';

/**
 * @ngdoc filter
 * @name currency
 * @kind function
 *
 * @description
 * Formats a number as a currency (ie $1,234.56). When no currency symbol is provided, default
 * symbol for current locale is used.
 *
 * @param {number} amount Input to filter.
 * @param {string=} symbol Currency symbol or identifier to be displayed.
 * @param {number=} fractionSize Number of decimal places to round the amount to, defaults to default max fraction size for current locale
 * @returns {string} Formatted number.
 *
 *
 * @example
   <example module="currencyExample" name="currency-filter">
     <file name="index.html">
       <script>
         angular.module('currencyExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.amount = 1234.56;
           }]);
       </script>
       <div ng-controller="ExampleController">
         <input type="number" ng-model="amount" aria-label="amount"> <br>
         default currency symbol ($): <span id="currency-default">{{amount | currency}}</span><br>
         custom currency identifier (USD$): <span id="currency-custom">{{amount | currency:"USD$"}}</span><br>
         no fractions (0): <span id="currency-no-fractions">{{amount | currency:"USD$":0}}</span>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should init with 1234.56', function() {
         expect(element(by.id('currency-default')).getText()).toBe('$1,234.56');
         expect(element(by.id('currency-custom')).getText()).toBe('USD$1,234.56');
         expect(element(by.id('currency-no-fractions')).getText()).toBe('USD$1,235');
       });
       it('should update', function() {
         if (browser.params.browser === 'safari') {
           // Safari does not understand the minus key. See
           // https://github.com/angular/protractor/issues/481
           return;
         }
         element(by.model('amount')).clear();
         element(by.model('amount')).sendKeys('-1234');
         expect(element(by.id('currency-default')).getText()).toBe('-$1,234.00');
         expect(element(by.id('currency-custom')).getText()).toBe('-USD$1,234.00');
         expect(element(by.id('currency-no-fractions')).getText()).toBe('-USD$1,234');
       });
     </file>
   </example>
 */
currencyFilter.$inject = ['$locale'];
function currencyFilter($locale) {
  var formats = $locale.NUMBER_FORMATS;
  return function(amount, currencySymbol, fractionSize) {
    if (isUndefined(currencySymbol)) {
      currencySymbol = formats.CURRENCY_SYM;
    }

    if (isUndefined(fractionSize)) {
      fractionSize = formats.PATTERNS[1].maxFrac;
    }

    // if null or undefined pass it through
    return (amount == null)
        ? amount
        : formatNumber(amount, formats.PATTERNS[1], formats.GROUP_SEP, formats.DECIMAL_SEP, fractionSize).
            replace(/\u00A4/g, currencySymbol);
  };
}

/**
 * @ngdoc filter
 * @name number
 * @kind function
 *
 * @description
 * Formats a number as text.
 *
 * If the input is null or undefined, it will just be returned.
 * If the input is infinite (Infinity or -Infinity), the Infinity symbol '∞' or '-∞' is returned, respectively.
 * If the input is not a number an empty string is returned.
 *
 *
 * @param {number|string} number Number to format.
 * @param {(number|string)=} fractionSize Number of decimal places to round the number to.
 * If this is not provided then the fraction size is computed from the current locale's number
 * formatting pattern. In the case of the default locale, it will be 3.
 * @returns {string} Number rounded to `fractionSize` appropriately formatted based on the current
 *                   locale (e.g., in the en_US locale it will have "." as the decimal separator and
 *                   include "," group separators after each third digit).
 *
 * @example
   <example module="numberFilterExample" name="number-filter">
     <file name="index.html">
       <script>
         angular.module('numberFilterExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.val = 1234.56789;
           }]);
       </script>
       <div ng-controller="ExampleController">
         <label>Enter number: <input ng-model='val'></label><br>
         Default formatting: <span id='number-default'>{{val | number}}</span><br>
         No fractions: <span>{{val | number:0}}</span><br>
         Negative number: <span>{{-val | number:4}}</span>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should format numbers', function() {
         expect(element(by.id('number-default')).getText()).toBe('1,234.568');
         expect(element(by.binding('val | number:0')).getText()).toBe('1,235');
         expect(element(by.binding('-val | number:4')).getText()).toBe('-1,234.5679');
       });

       it('should update', function() {
         element(by.model('val')).clear();
         element(by.model('val')).sendKeys('3374.333');
         expect(element(by.id('number-default')).getText()).toBe('3,374.333');
         expect(element(by.binding('val | number:0')).getText()).toBe('3,374');
         expect(element(by.binding('-val | number:4')).getText()).toBe('-3,374.3330');
      });
     </file>
   </example>
 */
numberFilter.$inject = ['$locale'];
function numberFilter($locale) {
  var formats = $locale.NUMBER_FORMATS;
  return function(number, fractionSize) {

    // if null or undefined pass it through
    return (number == null)
        ? number
        : formatNumber(number, formats.PATTERNS[0], formats.GROUP_SEP, formats.DECIMAL_SEP,
                       fractionSize);
  };
}

/**
 * Parse a number (as a string) into three components that can be used
 * for formatting the number.
 *
 * (Significant bits of this parse algorithm came from https://github.com/MikeMcl/big.js/)
 *
 * @param  {string} numStr The number to parse
 * @return {object} An object describing this number, containing the following keys:
 *  - d : an array of digits containing leading zeros as necessary
 *  - i : the number of the digits in `d` that are to the left of the decimal point
 *  - e : the exponent for numbers that would need more than `MAX_DIGITS` digits in `d`
 *
 */
function parse(numStr) {
  var exponent = 0, digits, numberOfIntegerDigits;
  var i, j, zeros;

  // Decimal point?
  if ((numberOfIntegerDigits = numStr.indexOf(DECIMAL_SEP)) > -1) {
    numStr = numStr.replace(DECIMAL_SEP, '');
  }

  // Exponential form?
  if ((i = numStr.search(/e/i)) > 0) {
    // Work out the exponent.
    if (numberOfIntegerDigits < 0) numberOfIntegerDigits = i;
    numberOfIntegerDigits += +numStr.slice(i + 1);
    numStr = numStr.substring(0, i);
  } else if (numberOfIntegerDigits < 0) {
    // There was no decimal point or exponent so it is an integer.
    numberOfIntegerDigits = numStr.length;
  }

  // Count the number of leading zeros.
  for (i = 0; numStr.charAt(i) === ZERO_CHAR; i++) { /* empty */ }

  if (i === (zeros = numStr.length)) {
    // The digits are all zero.
    digits = [0];
    numberOfIntegerDigits = 1;
  } else {
    // Count the number of trailing zeros
    zeros--;
    while (numStr.charAt(zeros) === ZERO_CHAR) zeros--;

    // Trailing zeros are insignificant so ignore them
    numberOfIntegerDigits -= i;
    digits = [];
    // Convert string to array of digits without leading/trailing zeros.
    for (j = 0; i <= zeros; i++, j++) {
      digits[j] = +numStr.charAt(i);
    }
  }

  // If the number overflows the maximum allowed digits then use an exponent.
  if (numberOfIntegerDigits > MAX_DIGITS) {
    digits = digits.splice(0, MAX_DIGITS - 1);
    exponent = numberOfIntegerDigits - 1;
    numberOfIntegerDigits = 1;
  }

  return { d: digits, e: exponent, i: numberOfIntegerDigits };
}

/**
 * Round the parsed number to the specified number of decimal places
 * This function changed the parsedNumber in-place
 */
function roundNumber(parsedNumber, fractionSize, minFrac, maxFrac) {
    var digits = parsedNumber.d;
    var fractionLen = digits.length - parsedNumber.i;

    // determine fractionSize if it is not specified; `+fractionSize` converts it to a number
    fractionSize = (isUndefined(fractionSize)) ? Math.min(Math.max(minFrac, fractionLen), maxFrac) : +fractionSize;

    // The index of the digit to where rounding is to occur
    var roundAt = fractionSize + parsedNumber.i;
    var digit = digits[roundAt];

    if (roundAt > 0) {
      // Drop fractional digits beyond `roundAt`
      digits.splice(Math.max(parsedNumber.i, roundAt));

      // Set non-fractional digits beyond `roundAt` to 0
      for (var j = roundAt; j < digits.length; j++) {
        digits[j] = 0;
      }
    } else {
      // We rounded to zero so reset the parsedNumber
      fractionLen = Math.max(0, fractionLen);
      parsedNumber.i = 1;
      digits.length = Math.max(1, roundAt = fractionSize + 1);
      digits[0] = 0;
      for (var i = 1; i < roundAt; i++) digits[i] = 0;
    }

    if (digit >= 5) {
      if (roundAt - 1 < 0) {
        for (var k = 0; k > roundAt; k--) {
          digits.unshift(0);
          parsedNumber.i++;
        }
        digits.unshift(1);
        parsedNumber.i++;
      } else {
        digits[roundAt - 1]++;
      }
    }

    // Pad out with zeros to get the required fraction length
    for (; fractionLen < Math.max(0, fractionSize); fractionLen++) digits.push(0);


    // Do any carrying, e.g. a digit was rounded up to 10
    var carry = digits.reduceRight(function(carry, d, i, digits) {
      d = d + carry;
      digits[i] = d % 10;
      return Math.floor(d / 10);
    }, 0);
    if (carry) {
      digits.unshift(carry);
      parsedNumber.i++;
    }
}

/**
 * Format a number into a string
 * @param  {number} number       The number to format
 * @param  {{
 *           minFrac, // the minimum number of digits required in the fraction part of the number
 *           maxFrac, // the maximum number of digits required in the fraction part of the number
 *           gSize,   // number of digits in each group of separated digits
 *           lgSize,  // number of digits in the last group of digits before the decimal separator
 *           negPre,  // the string to go in front of a negative number (e.g. `-` or `(`))
 *           posPre,  // the string to go in front of a positive number
 *           negSuf,  // the string to go after a negative number (e.g. `)`)
 *           posSuf   // the string to go after a positive number
 *         }} pattern
 * @param  {string} groupSep     The string to separate groups of number (e.g. `,`)
 * @param  {string} decimalSep   The string to act as the decimal separator (e.g. `.`)
 * @param  {[type]} fractionSize The size of the fractional part of the number
 * @return {string}              The number formatted as a string
 */
function formatNumber(number, pattern, groupSep, decimalSep, fractionSize) {

  if (!(isString(number) || isNumber(number)) || isNaN(number)) return '';

  var isInfinity = !isFinite(number);
  var isZero = false;
  var numStr = Math.abs(number) + '',
      formattedText = '',
      parsedNumber;

  if (isInfinity) {
    formattedText = '\u221e';
  } else {
    parsedNumber = parse(numStr);

    roundNumber(parsedNumber, fractionSize, pattern.minFrac, pattern.maxFrac);

    var digits = parsedNumber.d;
    var integerLen = parsedNumber.i;
    var exponent = parsedNumber.e;
    var decimals = [];
    isZero = digits.reduce(function(isZero, d) { return isZero && !d; }, true);

    // pad zeros for small numbers
    while (integerLen < 0) {
      digits.unshift(0);
      integerLen++;
    }

    // extract decimals digits
    if (integerLen > 0) {
      decimals = digits.splice(integerLen, digits.length);
    } else {
      decimals = digits;
      digits = [0];
    }

    // format the integer digits with grouping separators
    var groups = [];
    if (digits.length >= pattern.lgSize) {
      groups.unshift(digits.splice(-pattern.lgSize, digits.length).join(''));
    }
    while (digits.length > pattern.gSize) {
      groups.unshift(digits.splice(-pattern.gSize, digits.length).join(''));
    }
    if (digits.length) {
      groups.unshift(digits.join(''));
    }
    formattedText = groups.join(groupSep);

    // append the decimal digits
    if (decimals.length) {
      formattedText += decimalSep + decimals.join('');
    }

    if (exponent) {
      formattedText += 'e+' + exponent;
    }
  }
  if (number < 0 && !isZero) {
    return pattern.negPre + formattedText + pattern.negSuf;
  } else {
    return pattern.posPre + formattedText + pattern.posSuf;
  }
}

function padNumber(num, digits, trim, negWrap) {
  var neg = '';
  if (num < 0 || (negWrap && num <= 0)) {
    if (negWrap) {
      num = -num + 1;
    } else {
      num = -num;
      neg = '-';
    }
  }
  num = '' + num;
  while (num.length < digits) num = ZERO_CHAR + num;
  if (trim) {
    num = num.substr(num.length - digits);
  }
  return neg + num;
}


function dateGetter(name, size, offset, trim, negWrap) {
  offset = offset || 0;
  return function(date) {
    var value = date['get' + name]();
    if (offset > 0 || value > -offset) {
      value += offset;
    }
    if (value === 0 && offset === -12) value = 12;
    return padNumber(value, size, trim, negWrap);
  };
}

function dateStrGetter(name, shortForm, standAlone) {
  return function(date, formats) {
    var value = date['get' + name]();
    var propPrefix = (standAlone ? 'STANDALONE' : '') + (shortForm ? 'SHORT' : '');
    var get = uppercase(propPrefix + name);

    return formats[get][value];
  };
}

function timeZoneGetter(date, formats, offset) {
  var zone = -1 * offset;
  var paddedZone = (zone >= 0) ? '+' : '';

  paddedZone += padNumber(Math[zone > 0 ? 'floor' : 'ceil'](zone / 60), 2) +
                padNumber(Math.abs(zone % 60), 2);

  return paddedZone;
}

function getFirstThursdayOfYear(year) {
    // 0 = index of January
    var dayOfWeekOnFirst = (new Date(year, 0, 1)).getDay();
    // 4 = index of Thursday (+1 to account for 1st = 5)
    // 11 = index of *next* Thursday (+1 account for 1st = 12)
    return new Date(year, 0, ((dayOfWeekOnFirst <= 4) ? 5 : 12) - dayOfWeekOnFirst);
}

function getThursdayThisWeek(datetime) {
    return new Date(datetime.getFullYear(), datetime.getMonth(),
      // 4 = index of Thursday
      datetime.getDate() + (4 - datetime.getDay()));
}

function weekGetter(size) {
   return function(date) {
      var firstThurs = getFirstThursdayOfYear(date.getFullYear()),
         thisThurs = getThursdayThisWeek(date);

      var diff = +thisThurs - +firstThurs,
         result = 1 + Math.round(diff / 6.048e8); // 6.048e8 ms per week

      return padNumber(result, size);
   };
}

function ampmGetter(date, formats) {
  return date.getHours() < 12 ? formats.AMPMS[0] : formats.AMPMS[1];
}

function eraGetter(date, formats) {
  return date.getFullYear() <= 0 ? formats.ERAS[0] : formats.ERAS[1];
}

function longEraGetter(date, formats) {
  return date.getFullYear() <= 0 ? formats.ERANAMES[0] : formats.ERANAMES[1];
}

var DATE_FORMATS = {
  yyyy: dateGetter('FullYear', 4, 0, false, true),
    yy: dateGetter('FullYear', 2, 0, true, true),
     y: dateGetter('FullYear', 1, 0, false, true),
  MMMM: dateStrGetter('Month'),
   MMM: dateStrGetter('Month', true),
    MM: dateGetter('Month', 2, 1),
     M: dateGetter('Month', 1, 1),
  LLLL: dateStrGetter('Month', false, true),
    dd: dateGetter('Date', 2),
     d: dateGetter('Date', 1),
    HH: dateGetter('Hours', 2),
     H: dateGetter('Hours', 1),
    hh: dateGetter('Hours', 2, -12),
     h: dateGetter('Hours', 1, -12),
    mm: dateGetter('Minutes', 2),
     m: dateGetter('Minutes', 1),
    ss: dateGetter('Seconds', 2),
     s: dateGetter('Seconds', 1),
     // while ISO 8601 requires fractions to be prefixed with `.` or `,`
     // we can be just safely rely on using `sss` since we currently don't support single or two digit fractions
   sss: dateGetter('Milliseconds', 3),
  EEEE: dateStrGetter('Day'),
   EEE: dateStrGetter('Day', true),
     a: ampmGetter,
     Z: timeZoneGetter,
    ww: weekGetter(2),
     w: weekGetter(1),
     G: eraGetter,
     GG: eraGetter,
     GGG: eraGetter,
     GGGG: longEraGetter
};

var DATE_FORMATS_SPLIT = /((?:[^yMLdHhmsaZEwG']+)|(?:'(?:[^']|'')*')|(?:E+|y+|M+|L+|d+|H+|h+|m+|s+|a|Z|G+|w+))(.*)/,
    NUMBER_STRING = /^-?\d+$/;

/**
 * @ngdoc filter
 * @name date
 * @kind function
 *
 * @description
 *   Formats `date` to a string based on the requested `format`.
 *
 *   `format` string can be composed of the following elements:
 *
 *   * `'yyyy'`: 4 digit representation of year (e.g. AD 1 => 0001, AD 2010 => 2010)
 *   * `'yy'`: 2 digit representation of year, padded (00-99). (e.g. AD 2001 => 01, AD 2010 => 10)
 *   * `'y'`: 1 digit representation of year, e.g. (AD 1 => 1, AD 199 => 199)
 *   * `'MMMM'`: Month in year (January-December)
 *   * `'MMM'`: Month in year (Jan-Dec)
 *   * `'MM'`: Month in year, padded (01-12)
 *   * `'M'`: Month in year (1-12)
 *   * `'LLLL'`: Stand-alone month in year (January-December)
 *   * `'dd'`: Day in month, padded (01-31)
 *   * `'d'`: Day in month (1-31)
 *   * `'EEEE'`: Day in Week,(Sunday-Saturday)
 *   * `'EEE'`: Day in Week, (Sun-Sat)
 *   * `'HH'`: Hour in day, padded (00-23)
 *   * `'H'`: Hour in day (0-23)
 *   * `'hh'`: Hour in AM/PM, padded (01-12)
 *   * `'h'`: Hour in AM/PM, (1-12)
 *   * `'mm'`: Minute in hour, padded (00-59)
 *   * `'m'`: Minute in hour (0-59)
 *   * `'ss'`: Second in minute, padded (00-59)
 *   * `'s'`: Second in minute (0-59)
 *   * `'sss'`: Millisecond in second, padded (000-999)
 *   * `'a'`: AM/PM marker
 *   * `'Z'`: 4 digit (+sign) representation of the timezone offset (-1200-+1200)
 *   * `'ww'`: Week of year, padded (00-53). Week 01 is the week with the first Thursday of the year
 *   * `'w'`: Week of year (0-53). Week 1 is the week with the first Thursday of the year
 *   * `'G'`, `'GG'`, `'GGG'`: The abbreviated form of the era string (e.g. 'AD')
 *   * `'GGGG'`: The long form of the era string (e.g. 'Anno Domini')
 *
 *   `format` string can also be one of the following predefined
 *   {@link guide/i18n localizable formats}:
 *
 *   * `'medium'`: equivalent to `'MMM d, y h:mm:ss a'` for en_US locale
 *     (e.g. Sep 3, 2010 12:05:08 PM)
 *   * `'short'`: equivalent to `'M/d/yy h:mm a'` for en_US  locale (e.g. 9/3/10 12:05 PM)
 *   * `'fullDate'`: equivalent to `'EEEE, MMMM d, y'` for en_US  locale
 *     (e.g. Friday, September 3, 2010)
 *   * `'longDate'`: equivalent to `'MMMM d, y'` for en_US  locale (e.g. September 3, 2010)
 *   * `'mediumDate'`: equivalent to `'MMM d, y'` for en_US  locale (e.g. Sep 3, 2010)
 *   * `'shortDate'`: equivalent to `'M/d/yy'` for en_US locale (e.g. 9/3/10)
 *   * `'mediumTime'`: equivalent to `'h:mm:ss a'` for en_US locale (e.g. 12:05:08 PM)
 *   * `'shortTime'`: equivalent to `'h:mm a'` for en_US locale (e.g. 12:05 PM)
 *
 *   `format` string can contain literal values. These need to be escaped by surrounding with single quotes (e.g.
 *   `"h 'in the morning'"`). In order to output a single quote, escape it - i.e., two single quotes in a sequence
 *   (e.g. `"h 'o''clock'"`).
 *
 * @param {(Date|number|string)} date Date to format either as Date object, milliseconds (string or
 *    number) or various ISO 8601 datetime string formats (e.g. yyyy-MM-ddTHH:mm:ss.sssZ and its
 *    shorter versions like yyyy-MM-ddTHH:mmZ, yyyy-MM-dd or yyyyMMddTHHmmssZ). If no timezone is
 *    specified in the string input, the time is considered to be in the local timezone.
 * @param {string=} format Formatting rules (see Description). If not specified,
 *    `mediumDate` is used.
 * @param {string=} timezone Timezone to be used for formatting. It understands UTC/GMT and the
 *    continental US time zone abbreviations, but for general use, use a time zone offset, for
 *    example, `'+0430'` (4 hours, 30 minutes east of the Greenwich meridian)
 *    If not specified, the timezone of the browser will be used.
 * @returns {string} Formatted string or the input if input is not recognized as date/millis.
 *
 * @example
   <example name="filter-date">
     <file name="index.html">
       <span ng-non-bindable>{{1288323623006 | date:'medium'}}</span>:
           <span>{{1288323623006 | date:'medium'}}</span><br>
       <span ng-non-bindable>{{1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'}}</span>:
          <span>{{1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'}}</span><br>
       <span ng-non-bindable>{{1288323623006 | date:'MM/dd/yyyy @ h:mma'}}</span>:
          <span>{{'1288323623006' | date:'MM/dd/yyyy @ h:mma'}}</span><br>
       <span ng-non-bindable>{{1288323623006 | date:"MM/dd/yyyy 'at' h:mma"}}</span>:
          <span>{{'1288323623006' | date:"MM/dd/yyyy 'at' h:mma"}}</span><br>
     </file>
     <file name="protractor.js" type="protractor">
       it('should format date', function() {
         expect(element(by.binding("1288323623006 | date:'medium'")).getText()).
            toMatch(/Oct 2\d, 2010 \d{1,2}:\d{2}:\d{2} (AM|PM)/);
         expect(element(by.binding("1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'")).getText()).
            toMatch(/2010-10-2\d \d{2}:\d{2}:\d{2} (-|\+)?\d{4}/);
         expect(element(by.binding("'1288323623006' | date:'MM/dd/yyyy @ h:mma'")).getText()).
            toMatch(/10\/2\d\/2010 @ \d{1,2}:\d{2}(AM|PM)/);
         expect(element(by.binding("'1288323623006' | date:\"MM/dd/yyyy 'at' h:mma\"")).getText()).
            toMatch(/10\/2\d\/2010 at \d{1,2}:\d{2}(AM|PM)/);
       });
     </file>
   </example>
 */
dateFilter.$inject = ['$locale'];
function dateFilter($locale) {


  var R_ISO8601_STR = /^(\d{4})-?(\d\d)-?(\d\d)(?:T(\d\d)(?::?(\d\d)(?::?(\d\d)(?:\.(\d+))?)?)?(Z|([+-])(\d\d):?(\d\d))?)?$/;
                     // 1        2       3         4          5          6          7          8  9     10      11
  function jsonStringToDate(string) {
    var match;
    if ((match = string.match(R_ISO8601_STR))) {
      var date = new Date(0),
          tzHour = 0,
          tzMin  = 0,
          dateSetter = match[8] ? date.setUTCFullYear : date.setFullYear,
          timeSetter = match[8] ? date.setUTCHours : date.setHours;

      if (match[9]) {
        tzHour = toInt(match[9] + match[10]);
        tzMin = toInt(match[9] + match[11]);
      }
      dateSetter.call(date, toInt(match[1]), toInt(match[2]) - 1, toInt(match[3]));
      var h = toInt(match[4] || 0) - tzHour;
      var m = toInt(match[5] || 0) - tzMin;
      var s = toInt(match[6] || 0);
      var ms = Math.round(parseFloat('0.' + (match[7] || 0)) * 1000);
      timeSetter.call(date, h, m, s, ms);
      return date;
    }
    return string;
  }


  return function(date, format, timezone) {
    var text = '',
        parts = [],
        fn, match;

    format = format || 'mediumDate';
    format = $locale.DATETIME_FORMATS[format] || format;
    if (isString(date)) {
      date = NUMBER_STRING.test(date) ? toInt(date) : jsonStringToDate(date);
    }

    if (isNumber(date)) {
      date = new Date(date);
    }

    if (!isDate(date) || !isFinite(date.getTime())) {
      return date;
    }

    while (format) {
      match = DATE_FORMATS_SPLIT.exec(format);
      if (match) {
        parts = concat(parts, match, 1);
        format = parts.pop();
      } else {
        parts.push(format);
        format = null;
      }
    }

    var dateTimezoneOffset = date.getTimezoneOffset();
    if (timezone) {
      dateTimezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
      date = convertTimezoneToLocal(date, timezone, true);
    }
    forEach(parts, function(value) {
      fn = DATE_FORMATS[value];
      text += fn ? fn(date, $locale.DATETIME_FORMATS, dateTimezoneOffset)
                 : value === '\'\'' ? '\'' : value.replace(/(^'|'$)/g, '').replace(/''/g, '\'');
    });

    return text;
  };
}


/**
 * @ngdoc filter
 * @name json
 * @kind function
 *
 * @description
 *   Allows you to convert a JavaScript object into JSON string.
 *
 *   This filter is mostly useful for debugging. When using the double curly {{value}} notation
 *   the binding is automatically converted to JSON.
 *
 * @param {*} object Any JavaScript object (including arrays and primitive types) to filter.
 * @param {number=} spacing The number of spaces to use per indentation, defaults to 2.
 * @returns {string} JSON string.
 *
 *
 * @example
   <example name="filter-json">
     <file name="index.html">
       <pre id="default-spacing">{{ {'name':'value'} | json }}</pre>
       <pre id="custom-spacing">{{ {'name':'value'} | json:4 }}</pre>
     </file>
     <file name="protractor.js" type="protractor">
       it('should jsonify filtered objects', function() {
         expect(element(by.id('default-spacing')).getText()).toMatch(/\{\n {2}"name": ?"value"\n}/);
         expect(element(by.id('custom-spacing')).getText()).toMatch(/\{\n {4}"name": ?"value"\n}/);
       });
     </file>
   </example>
 *
 */
function jsonFilter() {
  return function(object, spacing) {
    if (isUndefined(spacing)) {
        spacing = 2;
    }
    return toJson(object, spacing);
  };
}


/**
 * @ngdoc filter
 * @name lowercase
 * @kind function
 * @description
 * Converts string to lowercase.
 * @see angular.lowercase
 */
var lowercaseFilter = valueFn(lowercase);


/**
 * @ngdoc filter
 * @name uppercase
 * @kind function
 * @description
 * Converts string to uppercase.
 * @see angular.uppercase
 */
var uppercaseFilter = valueFn(uppercase);

/**
 * @ngdoc filter
 * @name limitTo
 * @kind function
 *
 * @description
 * Creates a new array or string containing only a specified number of elements. The elements are
 * taken from either the beginning or the end of the source array, string or number, as specified by
 * the value and sign (positive or negative) of `limit`. Other array-like objects are also supported
 * (e.g. array subclasses, NodeLists, jqLite/jQuery collections etc). If a number is used as input,
 * it is converted to a string.
 *
 * @param {Array|ArrayLike|string|number} input - Array/array-like, string or number to be limited.
 * @param {string|number} limit - The length of the returned array or string. If the `limit` number
 *     is positive, `limit` number of items from the beginning of the source array/string are copied.
 *     If the number is negative, `limit` number  of items from the end of the source array/string
 *     are copied. The `limit` will be trimmed if it exceeds `array.length`. If `limit` is undefined,
 *     the input will be returned unchanged.
 * @param {(string|number)=} begin - Index at which to begin limitation. As a negative index,
 *     `begin` indicates an offset from the end of `input`. Defaults to `0`.
 * @returns {Array|string} A new sub-array or substring of length `limit` or less if the input had
 *     less than `limit` elements.
 *
 * @example
   <example module="limitToExample" name="limit-to-filter">
     <file name="index.html">
       <script>
         angular.module('limitToExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.numbers = [1,2,3,4,5,6,7,8,9];
             $scope.letters = "abcdefghi";
             $scope.longNumber = 2345432342;
             $scope.numLimit = 3;
             $scope.letterLimit = 3;
             $scope.longNumberLimit = 3;
           }]);
       </script>
       <div ng-controller="ExampleController">
         <label>
            Limit {{numbers}} to:
            <input type="number" step="1" ng-model="numLimit">
         </label>
         <p>Output numbers: {{ numbers | limitTo:numLimit }}</p>
         <label>
            Limit {{letters}} to:
            <input type="number" step="1" ng-model="letterLimit">
         </label>
         <p>Output letters: {{ letters | limitTo:letterLimit }}</p>
         <label>
            Limit {{longNumber}} to:
            <input type="number" step="1" ng-model="longNumberLimit">
         </label>
         <p>Output long number: {{ longNumber | limitTo:longNumberLimit }}</p>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       var numLimitInput = element(by.model('numLimit'));
       var letterLimitInput = element(by.model('letterLimit'));
       var longNumberLimitInput = element(by.model('longNumberLimit'));
       var limitedNumbers = element(by.binding('numbers | limitTo:numLimit'));
       var limitedLetters = element(by.binding('letters | limitTo:letterLimit'));
       var limitedLongNumber = element(by.binding('longNumber | limitTo:longNumberLimit'));

       it('should limit the number array to first three items', function() {
         expect(numLimitInput.getAttribute('value')).toBe('3');
         expect(letterLimitInput.getAttribute('value')).toBe('3');
         expect(longNumberLimitInput.getAttribute('value')).toBe('3');
         expect(limitedNumbers.getText()).toEqual('Output numbers: [1,2,3]');
         expect(limitedLetters.getText()).toEqual('Output letters: abc');
         expect(limitedLongNumber.getText()).toEqual('Output long number: 234');
       });

       // There is a bug in safari and protractor that doesn't like the minus key
       // it('should update the output when -3 is entered', function() {
       //   numLimitInput.clear();
       //   numLimitInput.sendKeys('-3');
       //   letterLimitInput.clear();
       //   letterLimitInput.sendKeys('-3');
       //   longNumberLimitInput.clear();
       //   longNumberLimitInput.sendKeys('-3');
       //   expect(limitedNumbers.getText()).toEqual('Output numbers: [7,8,9]');
       //   expect(limitedLetters.getText()).toEqual('Output letters: ghi');
       //   expect(limitedLongNumber.getText()).toEqual('Output long number: 342');
       // });

       it('should not exceed the maximum size of input array', function() {
         numLimitInput.clear();
         numLimitInput.sendKeys('100');
         letterLimitInput.clear();
         letterLimitInput.sendKeys('100');
         longNumberLimitInput.clear();
         longNumberLimitInput.sendKeys('100');
         expect(limitedNumbers.getText()).toEqual('Output numbers: [1,2,3,4,5,6,7,8,9]');
         expect(limitedLetters.getText()).toEqual('Output letters: abcdefghi');
         expect(limitedLongNumber.getText()).toEqual('Output long number: 2345432342');
       });
     </file>
   </example>
*/
function limitToFilter() {
  return function(input, limit, begin) {
    if (Math.abs(Number(limit)) === Infinity) {
      limit = Number(limit);
    } else {
      limit = toInt(limit);
    }
    if (isNumberNaN(limit)) return input;

    if (isNumber(input)) input = input.toString();
    if (!isArrayLike(input)) return input;

    begin = (!begin || isNaN(begin)) ? 0 : toInt(begin);
    begin = (begin < 0) ? Math.max(0, input.length + begin) : begin;

    if (limit >= 0) {
      return sliceFn(input, begin, begin + limit);
    } else {
      if (begin === 0) {
        return sliceFn(input, limit, input.length);
      } else {
        return sliceFn(input, Math.max(0, begin + limit), begin);
      }
    }
  };
}

function sliceFn(input, begin, end) {
  if (isString(input)) return input.slice(begin, end);

  return slice.call(input, begin, end);
}

/**
 * @ngdoc filter
 * @name orderBy
 * @kind function
 *
 * @description
 * Returns an array containing the items from the specified `collection`, ordered by a `comparator`
 * function based on the values computed using the `expression` predicate.
 *
 * For example, `[{id: 'foo'}, {id: 'bar'}] | orderBy:'id'` would result in
 * `[{id: 'bar'}, {id: 'foo'}]`.
 *
 * The `collection` can be an Array or array-like object (e.g. NodeList, jQuery object, TypedArray,
 * String, etc).
 *
 * The `expression` can be a single predicate, or a list of predicates each serving as a tie-breaker
 * for the preceding one. The `expression` is evaluated against each item and the output is used
 * for comparing with other items.
 *
 * You can change the sorting order by setting `reverse` to `true`. By default, items are sorted in
 * ascending order.
 *
 * The comparison is done using the `comparator` function. If none is specified, a default, built-in
 * comparator is used (see below for details - in a nutshell, it compares numbers numerically and
 * strings alphabetically).
 *
 * ### Under the hood
 *
 * Ordering the specified `collection` happens in two phases:
 *
 * 1. All items are passed through the predicate (or predicates), and the returned values are saved
 *    along with their type (`string`, `number` etc). For example, an item `{label: 'foo'}`, passed
 *    through a predicate that extracts the value of the `label` property, would be transformed to:
 *    ```
 *    {
 *      value: 'foo',
 *      type: 'string',
 *      index: ...
 *    }
 *    ```
 * 2. The comparator function is used to sort the items, based on the derived values, types and
 *    indices.
 *
 * If you use a custom comparator, it will be called with pairs of objects of the form
 * `{value: ..., type: '...', index: ...}` and is expected to return `0` if the objects are equal
 * (as far as the comparator is concerned), `-1` if the 1st one should be ranked higher than the
 * second, or `1` otherwise.
 *
 * In order to ensure that the sorting will be deterministic across platforms, if none of the
 * specified predicates can distinguish between two items, `orderBy` will automatically introduce a
 * dummy predicate that returns the item's index as `value`.
 * (If you are using a custom comparator, make sure it can handle this predicate as well.)
 *
 * Finally, in an attempt to simplify things, if a predicate returns an object as the extracted
 * value for an item, `orderBy` will try to convert that object to a primitive value, before passing
 * it to the comparator. The following rules govern the conversion:
 *
 * 1. If the object has a `valueOf()` method that returns a primitive, its return value will be
 *    used instead.<br />
 *    (If the object has a `valueOf()` method that returns another object, then the returned object
 *    will be used in subsequent steps.)
 * 2. If the object has a custom `toString()` method (i.e. not the one inherited from `Object`) that
 *    returns a primitive, its return value will be used instead.<br />
 *    (If the object has a `toString()` method that returns another object, then the returned object
 *    will be used in subsequent steps.)
 * 3. No conversion; the object itself is used.
 *
 * ### The default comparator
 *
 * The default, built-in comparator should be sufficient for most usecases. In short, it compares
 * numbers numerically, strings alphabetically (and case-insensitively), for objects falls back to
 * using their index in the original collection, and sorts values of different types by type.
 *
 * More specifically, it follows these steps to determine the relative order of items:
 *
 * 1. If the compared values are of different types, compare the types themselves alphabetically.
 * 2. If both values are of type `string`, compare them alphabetically in a case- and
 *    locale-insensitive way.
 * 3. If both values are objects, compare their indices instead.
 * 4. Otherwise, return:
 *    -  `0`, if the values are equal (by strict equality comparison, i.e. using `===`).
 *    - `-1`, if the 1st value is "less than" the 2nd value (compared using the `<` operator).
 *    -  `1`, otherwise.
 *
 * **Note:** If you notice numbers not being sorted as expected, make sure they are actually being
 *           saved as numbers and not strings.
 * **Note:** For the purpose of sorting, `null` values are treated as the string `'null'` (i.e.
 *           `type: 'string'`, `value: 'null'`). This may cause unexpected sort order relative to
 *           other values.
 *
 * @param {Array|ArrayLike} collection - The collection (array or array-like object) to sort.
 * @param {(Function|string|Array.<Function|string>)=} expression - A predicate (or list of
 *    predicates) to be used by the comparator to determine the order of elements.
 *
 *    Can be one of:
 *
 *    - `Function`: A getter function. This function will be called with each item as argument and
 *      the return value will be used for sorting.
 *    - `string`: An Angular expression. This expression will be evaluated against each item and the
 *      result will be used for sorting. For example, use `'label'` to sort by a property called
 *      `label` or `'label.substring(0, 3)'` to sort by the first 3 characters of the `label`
 *      property.<br />
 *      (The result of a constant expression is interpreted as a property name to be used for
 *      comparison. For example, use `'"special name"'` (note the extra pair of quotes) to sort by a
 *      property called `special name`.)<br />
 *      An expression can be optionally prefixed with `+` or `-` to control the sorting direction,
 *      ascending or descending. For example, `'+label'` or `'-label'`. If no property is provided,
 *      (e.g. `'+'` or `'-'`), the collection element itself is used in comparisons.
 *    - `Array`: An array of function and/or string predicates. If a predicate cannot determine the
 *      relative order of two items, the next predicate is used as a tie-breaker.
 *
 * **Note:** If the predicate is missing or empty then it defaults to `'+'`.
 *
 * @param {boolean=} reverse - If `true`, reverse the sorting order.
 * @param {(Function)=} comparator - The comparator function used to determine the relative order of
 *    value pairs. If omitted, the built-in comparator will be used.
 *
 * @returns {Array} - The sorted array.
 *
 *
 * @example
 * ### Ordering a table with `ngRepeat`
 *
 * The example below demonstrates a simple {@link ngRepeat ngRepeat}, where the data is sorted by
 * age in descending order (expression is set to `'-age'`). The `comparator` is not set, which means
 * it defaults to the built-in comparator.
 *
   <example name="orderBy-static" module="orderByExample1">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <table class="friends">
           <tr>
             <th>Name</th>
             <th>Phone Number</th>
             <th>Age</th>
           </tr>
           <tr ng-repeat="friend in friends | orderBy:'-age'">
             <td>{{friend.name}}</td>
             <td>{{friend.phone}}</td>
             <td>{{friend.age}}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample1', [])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.friends = [
             {name: 'John',   phone: '555-1212',  age: 10},
             {name: 'Mary',   phone: '555-9876',  age: 19},
             {name: 'Mike',   phone: '555-4321',  age: 21},
             {name: 'Adam',   phone: '555-5678',  age: 35},
             {name: 'Julie',  phone: '555-8765',  age: 29}
           ];
         }]);
     </file>
     <file name="style.css">
       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var names = element.all(by.repeater('friends').column('friend.name'));

       it('should sort friends by age in reverse order', function() {
         expect(names.get(0).getText()).toBe('Adam');
         expect(names.get(1).getText()).toBe('Julie');
         expect(names.get(2).getText()).toBe('Mike');
         expect(names.get(3).getText()).toBe('Mary');
         expect(names.get(4).getText()).toBe('John');
       });
     </file>
   </example>
 * <hr />
 *
 * @example
 * ### Changing parameters dynamically
 *
 * All parameters can be changed dynamically. The next example shows how you can make the columns of
 * a table sortable, by binding the `expression` and `reverse` parameters to scope properties.
 *
   <example name="orderBy-dynamic" module="orderByExample2">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <pre>Sort by = {{propertyName}}; reverse = {{reverse}}</pre>
         <hr/>
         <button ng-click="propertyName = null; reverse = false">Set to unsorted</button>
         <hr/>
         <table class="friends">
           <tr>
             <th>
               <button ng-click="sortBy('name')">Name</button>
               <span class="sortorder" ng-show="propertyName === 'name'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('phone')">Phone Number</button>
               <span class="sortorder" ng-show="propertyName === 'phone'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('age')">Age</button>
               <span class="sortorder" ng-show="propertyName === 'age'" ng-class="{reverse: reverse}"></span>
             </th>
           </tr>
           <tr ng-repeat="friend in friends | orderBy:propertyName:reverse">
             <td>{{friend.name}}</td>
             <td>{{friend.phone}}</td>
             <td>{{friend.age}}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample2', [])
         .controller('ExampleController', ['$scope', function($scope) {
           var friends = [
             {name: 'John',   phone: '555-1212',  age: 10},
             {name: 'Mary',   phone: '555-9876',  age: 19},
             {name: 'Mike',   phone: '555-4321',  age: 21},
             {name: 'Adam',   phone: '555-5678',  age: 35},
             {name: 'Julie',  phone: '555-8765',  age: 29}
           ];

           $scope.propertyName = 'age';
           $scope.reverse = true;
           $scope.friends = friends;

           $scope.sortBy = function(propertyName) {
             $scope.reverse = ($scope.propertyName === propertyName) ? !$scope.reverse : false;
             $scope.propertyName = propertyName;
           };
         }]);
     </file>
     <file name="style.css">
       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }

       .sortorder:after {
         content: '\25b2';   // BLACK UP-POINTING TRIANGLE
       }
       .sortorder.reverse:after {
         content: '\25bc';   // BLACK DOWN-POINTING TRIANGLE
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var unsortButton = element(by.partialButtonText('unsorted'));
       var nameHeader = element(by.partialButtonText('Name'));
       var phoneHeader = element(by.partialButtonText('Phone'));
       var ageHeader = element(by.partialButtonText('Age'));
       var firstName = element(by.repeater('friends').column('friend.name').row(0));
       var lastName = element(by.repeater('friends').column('friend.name').row(4));

       it('should sort friends by some property, when clicking on the column header', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         phoneHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Mary');

         nameHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('Mike');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');
       });

       it('should sort friends in reverse order, when clicking on the same column', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');

         ageHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');
       });

       it('should restore the original order, when clicking "Set to unsorted"', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         unsortButton.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Julie');
       });
     </file>
   </example>
 * <hr />
 *
 * @example
 * ### Using `orderBy` inside a controller
 *
 * It is also possible to call the `orderBy` filter manually, by injecting `orderByFilter`, and
 * calling it with the desired parameters. (Alternatively, you could inject the `$filter` factory
 * and retrieve the `orderBy` filter with `$filter('orderBy')`.)
 *
   <example name="orderBy-call-manually" module="orderByExample3">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <pre>Sort by = {{propertyName}}; reverse = {{reverse}}</pre>
         <hr/>
         <button ng-click="sortBy(null)">Set to unsorted</button>
         <hr/>
         <table class="friends">
           <tr>
             <th>
               <button ng-click="sortBy('name')">Name</button>
               <span class="sortorder" ng-show="propertyName === 'name'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('phone')">Phone Number</button>
               <span class="sortorder" ng-show="propertyName === 'phone'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('age')">Age</button>
               <span class="sortorder" ng-show="propertyName === 'age'" ng-class="{reverse: reverse}"></span>
             </th>
           </tr>
           <tr ng-repeat="friend in friends">
             <td>{{friend.name}}</td>
             <td>{{friend.phone}}</td>
             <td>{{friend.age}}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample3', [])
         .controller('ExampleController', ['$scope', 'orderByFilter', function($scope, orderBy) {
           var friends = [
             {name: 'John',   phone: '555-1212',  age: 10},
             {name: 'Mary',   phone: '555-9876',  age: 19},
             {name: 'Mike',   phone: '555-4321',  age: 21},
             {name: 'Adam',   phone: '555-5678',  age: 35},
             {name: 'Julie',  phone: '555-8765',  age: 29}
           ];

           $scope.propertyName = 'age';
           $scope.reverse = true;
           $scope.friends = orderBy(friends, $scope.propertyName, $scope.reverse);

           $scope.sortBy = function(propertyName) {
             $scope.reverse = (propertyName !== null && $scope.propertyName === propertyName)
                 ? !$scope.reverse : false;
             $scope.propertyName = propertyName;
             $scope.friends = orderBy(friends, $scope.propertyName, $scope.reverse);
           };
         }]);
     </file>
     <file name="style.css">
       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }

       .sortorder:after {
         content: '\25b2';   // BLACK UP-POINTING TRIANGLE
       }
       .sortorder.reverse:after {
         content: '\25bc';   // BLACK DOWN-POINTING TRIANGLE
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var unsortButton = element(by.partialButtonText('unsorted'));
       var nameHeader = element(by.partialButtonText('Name'));
       var phoneHeader = element(by.partialButtonText('Phone'));
       var ageHeader = element(by.partialButtonText('Age'));
       var firstName = element(by.repeater('friends').column('friend.name').row(0));
       var lastName = element(by.repeater('friends').column('friend.name').row(4));

       it('should sort friends by some property, when clicking on the column header', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         phoneHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Mary');

         nameHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('Mike');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');
       });

       it('should sort friends in reverse order, when clicking on the same column', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');

         ageHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');
       });

       it('should restore the original order, when clicking "Set to unsorted"', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         unsortButton.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Julie');
       });
     </file>
   </example>
 * <hr />
 *
 * @example
 * ### Using a custom comparator
 *
 * If you have very specific requirements about the way items are sorted, you can pass your own
 * comparator function. For example, you might need to compare some strings in a locale-sensitive
 * way. (When specifying a custom comparator, you also need to pass a value for the `reverse`
 * argument - passing `false` retains the default sorting order, i.e. ascending.)
 *
   <example name="orderBy-custom-comparator" module="orderByExample4">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <div class="friends-container custom-comparator">
           <h3>Locale-sensitive Comparator</h3>
           <table class="friends">
             <tr>
               <th>Name</th>
               <th>Favorite Letter</th>
             </tr>
             <tr ng-repeat="friend in friends | orderBy:'favoriteLetter':false:localeSensitiveComparator">
               <td>{{friend.name}}</td>
               <td>{{friend.favoriteLetter}}</td>
             </tr>
           </table>
         </div>
         <div class="friends-container default-comparator">
           <h3>Default Comparator</h3>
           <table class="friends">
             <tr>
               <th>Name</th>
               <th>Favorite Letter</th>
             </tr>
             <tr ng-repeat="friend in friends | orderBy:'favoriteLetter'">
               <td>{{friend.name}}</td>
               <td>{{friend.favoriteLetter}}</td>
             </tr>
           </table>
         </div>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample4', [])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.friends = [
             {name: 'John',   favoriteLetter: 'Ä'},
             {name: 'Mary',   favoriteLetter: 'Ü'},
             {name: 'Mike',   favoriteLetter: 'Ö'},
             {name: 'Adam',   favoriteLetter: 'H'},
             {name: 'Julie',  favoriteLetter: 'Z'}
           ];

           $scope.localeSensitiveComparator = function(v1, v2) {
             // If we don't get strings, just compare by index
             if (v1.type !== 'string' || v2.type !== 'string') {
               return (v1.index < v2.index) ? -1 : 1;
             }

             // Compare strings alphabetically, taking locale into account
             return v1.value.localeCompare(v2.value);
           };
         }]);
     </file>
     <file name="style.css">
       .friends-container {
         display: inline-block;
         margin: 0 30px;
       }

       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var container = element(by.css('.custom-comparator'));
       var names = container.all(by.repeater('friends').column('friend.name'));

       it('should sort friends by favorite letter (in correct alphabetical order)', function() {
         expect(names.get(0).getText()).toBe('John');
         expect(names.get(1).getText()).toBe('Adam');
         expect(names.get(2).getText()).toBe('Mike');
         expect(names.get(3).getText()).toBe('Mary');
         expect(names.get(4).getText()).toBe('Julie');
       });
     </file>
   </example>
 *
 */
orderByFilter.$inject = ['$parse'];
function orderByFilter($parse) {
  return function(array, sortPredicate, reverseOrder, compareFn) {

    if (array == null) return array;
    if (!isArrayLike(array)) {
      throw minErr('orderBy')('notarray', 'Expected array but received: {0}', array);
    }

    if (!isArray(sortPredicate)) { sortPredicate = [sortPredicate]; }
    if (sortPredicate.length === 0) { sortPredicate = ['+']; }

    var predicates = processPredicates(sortPredicate);

    var descending = reverseOrder ? -1 : 1;

    // Define the `compare()` function. Use a default comparator if none is specified.
    var compare = isFunction(compareFn) ? compareFn : defaultCompare;

    // The next three lines are a version of a Swartzian Transform idiom from Perl
    // (sometimes called the Decorate-Sort-Undecorate idiom)
    // See https://en.wikipedia.org/wiki/Schwartzian_transform
    var compareValues = Array.prototype.map.call(array, getComparisonObject);
    compareValues.sort(doComparison);
    array = compareValues.map(function(item) { return item.value; });

    return array;

    function getComparisonObject(value, index) {
      // NOTE: We are adding an extra `tieBreaker` value based on the element's index.
      // This will be used to keep the sort stable when none of the input predicates can
      // distinguish between two elements.
      return {
        value: value,
        tieBreaker: {value: index, type: 'number', index: index},
        predicateValues: predicates.map(function(predicate) {
          return getPredicateValue(predicate.get(value), index);
        })
      };
    }

    function doComparison(v1, v2) {
      for (var i = 0, ii = predicates.length; i < ii; i++) {
        var result = compare(v1.predicateValues[i], v2.predicateValues[i]);
        if (result) {
          return result * predicates[i].descending * descending;
        }
      }

      return compare(v1.tieBreaker, v2.tieBreaker) * descending;
    }
  };

  function processPredicates(sortPredicates) {
    return sortPredicates.map(function(predicate) {
      var descending = 1, get = identity;

      if (isFunction(predicate)) {
        get = predicate;
      } else if (isString(predicate)) {
        if ((predicate.charAt(0) === '+' || predicate.charAt(0) === '-')) {
          descending = predicate.charAt(0) === '-' ? -1 : 1;
          predicate = predicate.substring(1);
        }
        if (predicate !== '') {
          get = $parse(predicate);
          if (get.constant) {
            var key = get();
            get = function(value) { return value[key]; };
          }
        }
      }
      return {get: get, descending: descending};
    });
  }

  function isPrimitive(value) {
    switch (typeof value) {
      case 'number': /* falls through */
      case 'boolean': /* falls through */
      case 'string':
        return true;
      default:
        return false;
    }
  }

  function objectValue(value) {
    // If `valueOf` is a valid function use that
    if (isFunction(value.valueOf)) {
      value = value.valueOf();
      if (isPrimitive(value)) return value;
    }
    // If `toString` is a valid function and not the one from `Object.prototype` use that
    if (hasCustomToString(value)) {
      value = value.toString();
      if (isPrimitive(value)) return value;
    }

    return value;
  }

  function getPredicateValue(value, index) {
    var type = typeof value;
    if (value === null) {
      type = 'string';
      value = 'null';
    } else if (type === 'object') {
      value = objectValue(value);
    }
    return {value: value, type: type, index: index};
  }

  function defaultCompare(v1, v2) {
    var result = 0;
    var type1 = v1.type;
    var type2 = v2.type;

    if (type1 === type2) {
      var value1 = v1.value;
      var value2 = v2.value;

      if (type1 === 'string') {
        // Compare strings case-insensitively
        value1 = value1.toLowerCase();
        value2 = value2.toLowerCase();
      } else if (type1 === 'object') {
        // For basic objects, use the position of the object
        // in the collection instead of the value
        if (isObject(value1)) value1 = v1.index;
        if (isObject(value2)) value2 = v2.index;
      }

      if (value1 !== value2) {
        result = value1 < value2 ? -1 : 1;
      }
    } else {
      result = type1 < type2 ? -1 : 1;
    }

    return result;
  }
}

function ngDirective(directive) {
  if (isFunction(directive)) {
    directive = {
      link: directive
    };
  }
  directive.restrict = directive.restrict || 'AC';
  return valueFn(directive);
}

/**
 * @ngdoc directive
 * @name a
 * @restrict E
 *
 * @description
 * Modifies the default behavior of the html a tag so that the default action is prevented when
 * the href attribute is empty.
 *
 * For dynamically creating `href` attributes for a tags, see the {@link ng.ngHref `ngHref`} directive.
 */
var htmlAnchorDirective = valueFn({
  restrict: 'E',
  compile: function(element, attr) {
    if (!attr.href && !attr.xlinkHref) {
      return function(scope, element) {
        // If the linked element is not an anchor tag anymore, do nothing
        if (element[0].nodeName.toLowerCase() !== 'a') return;

        // SVGAElement does not use the href attribute, but rather the 'xlinkHref' attribute.
        var href = toString.call(element.prop('href')) === '[object SVGAnimatedString]' ?
                   'xlink:href' : 'href';
        element.on('click', function(event) {
          // if we have no href url, then don't navigate anywhere.
          if (!element.attr(href)) {
            event.preventDefault();
          }
        });
      };
    }
  }
});

/**
 * @ngdoc directive
 * @name ngHref
 * @restrict A
 * @priority 99
 *
 * @description
 * Using Angular markup like `{{hash}}` in an href attribute will
 * make the link go to the wrong URL if the user clicks it before
 * Angular has a chance to replace the `{{hash}}` markup with its
 * value. Until Angular replaces the markup the link will be broken
 * and will most likely return a 404 error. The `ngHref` directive
 * solves this problem.
 *
 * The wrong way to write it:
 * ```html
 * <a href="http://www.gravatar.com/avatar/{{hash}}">link1</a>
 * ```
 *
 * The correct way to write it:
 * ```html
 * <a ng-href="http://www.gravatar.com/avatar/{{hash}}">link1</a>
 * ```
 *
 * @element A
 * @param {template} ngHref any string which can contain `{{}}` markup.
 *
 * @example
 * This example shows various combinations of `href`, `ng-href` and `ng-click` attributes
 * in links and their different behaviors:
    <example name="ng-href">
      <file name="index.html">
        <input ng-model="value" /><br />
        <a id="link-1" href ng-click="value = 1">link 1</a> (link, don't reload)<br />
        <a id="link-2" href="" ng-click="value = 2">link 2</a> (link, don't reload)<br />
        <a id="link-3" ng-href="/{{'123'}}">link 3</a> (link, reload!)<br />
        <a id="link-4" href="" name="xx" ng-click="value = 4">anchor</a> (link, don't reload)<br />
        <a id="link-5" name="xxx" ng-click="value = 5">anchor</a> (no link)<br />
        <a id="link-6" ng-href="{{value}}">link</a> (link, change location)
      </file>
      <file name="protractor.js" type="protractor">
        it('should execute ng-click but not reload when href without value', function() {
          element(by.id('link-1')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('1');
          expect(element(by.id('link-1')).getAttribute('href')).toBe('');
        });

        it('should execute ng-click but not reload when href empty string', function() {
          element(by.id('link-2')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('2');
          expect(element(by.id('link-2')).getAttribute('href')).toBe('');
        });

        it('should execute ng-click and change url when ng-href specified', function() {
          expect(element(by.id('link-3')).getAttribute('href')).toMatch(/\/123$/);

          element(by.id('link-3')).click();

          // At this point, we navigate away from an Angular page, so we need
          // to use browser.driver to get the base webdriver.

          browser.wait(function() {
            return browser.driver.getCurrentUrl().then(function(url) {
              return url.match(/\/123$/);
            });
          }, 5000, 'page should navigate to /123');
        });

        it('should execute ng-click but not reload when href empty string and name specified', function() {
          element(by.id('link-4')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('4');
          expect(element(by.id('link-4')).getAttribute('href')).toBe('');
        });

        it('should execute ng-click but not reload when no href but name specified', function() {
          element(by.id('link-5')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('5');
          expect(element(by.id('link-5')).getAttribute('href')).toBe(null);
        });

        it('should only change url when only ng-href', function() {
          element(by.model('value')).clear();
          element(by.model('value')).sendKeys('6');
          expect(element(by.id('link-6')).getAttribute('href')).toMatch(/\/6$/);

          element(by.id('link-6')).click();

          // At this point, we navigate away from an Angular page, so we need
          // to use browser.driver to get the base webdriver.
          browser.wait(function() {
            return browser.driver.getCurrentUrl().then(function(url) {
              return url.match(/\/6$/);
            });
          }, 5000, 'page should navigate to /6');
        });
      </file>
    </example>
 */

/**
 * @ngdoc directive
 * @name ngSrc
 * @restrict A
 * @priority 99
 *
 * @description
 * Using Angular markup like `{{hash}}` in a `src` attribute doesn't
 * work right: The browser will fetch from the URL with the literal
 * text `{{hash}}` until Angular replaces the expression inside
 * `{{hash}}`. The `ngSrc` directive solves this problem.
 *
 * The buggy way to write it:
 * ```html
 * <img src="http://www.gravatar.com/avatar/{{hash}}" alt="Description"/>
 * ```
 *
 * The correct way to write it:
 * ```html
 * <img ng-src="http://www.gravatar.com/avatar/{{hash}}" alt="Description" />
 * ```
 *
 * @element IMG
 * @param {template} ngSrc any string which can contain `{{}}` markup.
 */

/**
 * @ngdoc directive
 * @name ngSrcset
 * @restrict A
 * @priority 99
 *
 * @description
 * Using Angular markup like `{{hash}}` in a `srcset` attribute doesn't
 * work right: The browser will fetch from the URL with the literal
 * text `{{hash}}` until Angular replaces the expression inside
 * `{{hash}}`. The `ngSrcset` directive solves this problem.
 *
 * The buggy way to write it:
 * ```html
 * <img srcset="http://www.gravatar.com/avatar/{{hash}} 2x" alt="Description"/>
 * ```
 *
 * The correct way to write it:
 * ```html
 * <img ng-srcset="http://www.gravatar.com/avatar/{{hash}} 2x" alt="Description" />
 * ```
 *
 * @element IMG
 * @param {template} ngSrcset any string which can contain `{{}}` markup.
 */

/**
 * @ngdoc directive
 * @name ngDisabled
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * This directive sets the `disabled` attribute on the element (typically a form control,
 * e.g. `input`, `button`, `select` etc.) if the
 * {@link guide/expression expression} inside `ngDisabled` evaluates to truthy.
 *
 * A special directive is necessary because we cannot use interpolation inside the `disabled`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * @example
    <example name="ng-disabled">
      <file name="index.html">
        <label>Click me to toggle: <input type="checkbox" ng-model="checked"></label><br/>
        <button ng-model="button" ng-disabled="checked">Button</button>
      </file>
      <file name="protractor.js" type="protractor">
        it('should toggle button', function() {
          expect(element(by.css('button')).getAttribute('disabled')).toBeFalsy();
          element(by.model('checked')).click();
          expect(element(by.css('button')).getAttribute('disabled')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element INPUT
 * @param {expression} ngDisabled If the {@link guide/expression expression} is truthy,
 *     then the `disabled` attribute will be set on the element
 */


/**
 * @ngdoc directive
 * @name ngChecked
 * @restrict A
 * @priority 100
 *
 * @description
 * Sets the `checked` attribute on the element, if the expression inside `ngChecked` is truthy.
 *
 * Note that this directive should not be used together with {@link ngModel `ngModel`},
 * as this can lead to unexpected behavior.
 *
 * A special directive is necessary because we cannot use interpolation inside the `checked`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * @example
    <example name="ng-checked">
      <file name="index.html">
        <label>Check me to check both: <input type="checkbox" ng-model="master"></label><br/>
        <input id="checkSlave" type="checkbox" ng-checked="master" aria-label="Slave input">
      </file>
      <file name="protractor.js" type="protractor">
        it('should check both checkBoxes', function() {
          expect(element(by.id('checkSlave')).getAttribute('checked')).toBeFalsy();
          element(by.model('master')).click();
          expect(element(by.id('checkSlave')).getAttribute('checked')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element INPUT
 * @param {expression} ngChecked If the {@link guide/expression expression} is truthy,
 *     then the `checked` attribute will be set on the element
 */


/**
 * @ngdoc directive
 * @name ngReadonly
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * Sets the `readonly` attribute on the element, if the expression inside `ngReadonly` is truthy.
 * Note that `readonly` applies only to `input` elements with specific types. [See the input docs on
 * MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-readonly) for more information.
 *
 * A special directive is necessary because we cannot use interpolation inside the `readonly`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * @example
    <example name="ng-readonly">
      <file name="index.html">
        <label>Check me to make text readonly: <input type="checkbox" ng-model="checked"></label><br/>
        <input type="text" ng-readonly="checked" value="I'm Angular" aria-label="Readonly field" />
      </file>
      <file name="protractor.js" type="protractor">
        it('should toggle readonly attr', function() {
          expect(element(by.css('[type="text"]')).getAttribute('readonly')).toBeFalsy();
          element(by.model('checked')).click();
          expect(element(by.css('[type="text"]')).getAttribute('readonly')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element INPUT
 * @param {expression} ngReadonly If the {@link guide/expression expression} is truthy,
 *     then special attribute "readonly" will be set on the element
 */


/**
 * @ngdoc directive
 * @name ngSelected
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * Sets the `selected` attribute on the element, if the expression inside `ngSelected` is truthy.
 *
 * A special directive is necessary because we cannot use interpolation inside the `selected`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * <div class="alert alert-warning">
 *   **Note:** `ngSelected` does not interact with the `select` and `ngModel` directives, it only
 *   sets the `selected` attribute on the element. If you are using `ngModel` on the select, you
 *   should not use `ngSelected` on the options, as `ngModel` will set the select value and
 *   selected options.
 * </div>
 *
 * @example
    <example name="ng-selected">
      <file name="index.html">
        <label>Check me to select: <input type="checkbox" ng-model="selected"></label><br/>
        <select aria-label="ngSelected demo">
          <option>Hello!</option>
          <option id="greet" ng-selected="selected">Greetings!</option>
        </select>
      </file>
      <file name="protractor.js" type="protractor">
        it('should select Greetings!', function() {
          expect(element(by.id('greet')).getAttribute('selected')).toBeFalsy();
          element(by.model('selected')).click();
          expect(element(by.id('greet')).getAttribute('selected')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element OPTION
 * @param {expression} ngSelected If the {@link guide/expression expression} is truthy,
 *     then special attribute "selected" will be set on the element
 */

/**
 * @ngdoc directive
 * @name ngOpen
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * Sets the `open` attribute on the element, if the expression inside `ngOpen` is truthy.
 *
 * A special directive is necessary because we cannot use interpolation inside the `open`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * ## A note about browser compatibility
 *
 * Edge, Firefox, and Internet Explorer do not support the `details` element, it is
 * recommended to use {@link ng.ngShow} and {@link ng.ngHide} instead.
 *
 * @example
     <example name="ng-open">
       <file name="index.html">
         <label>Check me check multiple: <input type="checkbox" ng-model="open"></label><br/>
         <details id="details" ng-open="open">
            <summary>Show/Hide me</summary>
         </details>
       </file>
       <file name="protractor.js" type="protractor">
         it('should toggle open', function() {
           expect(element(by.id('details')).getAttribute('open')).toBeFalsy();
           element(by.model('open')).click();
           expect(element(by.id('details')).getAttribute('open')).toBeTruthy();
         });
       </file>
     </example>
 *
 * @element DETAILS
 * @param {expression} ngOpen If the {@link guide/expression expression} is truthy,
 *     then special attribute "open" will be set on the element
 */

var ngAttributeAliasDirectives = {};

// boolean attrs are evaluated
forEach(BOOLEAN_ATTR, function(propName, attrName) {
  // binding to multiple is not supported
  if (propName === 'multiple') return;

  function defaultLinkFn(scope, element, attr) {
    scope.$watch(attr[normalized], function ngBooleanAttrWatchAction(value) {
      attr.$set(attrName, !!value);
    });
  }

  var normalized = directiveNormalize('ng-' + attrName);
  var linkFn = defaultLinkFn;

  if (propName === 'checked') {
    linkFn = function(scope, element, attr) {
      // ensuring ngChecked doesn't interfere with ngModel when both are set on the same input
      if (attr.ngModel !== attr[normalized]) {
        defaultLinkFn(scope, element, attr);
      }
    };
  }

  ngAttributeAliasDirectives[normalized] = function() {
    return {
      restrict: 'A',
      priority: 100,
      link: linkFn
    };
  };
});

// aliased input attrs are evaluated
forEach(ALIASED_ATTR, function(htmlAttr, ngAttr) {
  ngAttributeAliasDirectives[ngAttr] = function() {
    return {
      priority: 100,
      link: function(scope, element, attr) {
        //special case ngPattern when a literal regular expression value
        //is used as the expression (this way we don't have to watch anything).
        if (ngAttr === 'ngPattern' && attr.ngPattern.charAt(0) === '/') {
          var match = attr.ngPattern.match(REGEX_STRING_REGEXP);
          if (match) {
            attr.$set('ngPattern', new RegExp(match[1], match[2]));
            return;
          }
        }

        scope.$watch(attr[ngAttr], function ngAttrAliasWatchAction(value) {
          attr.$set(ngAttr, value);
        });
      }
    };
  };
});

// ng-src, ng-srcset, ng-href are interpolated
forEach(['src', 'srcset', 'href'], function(attrName) {
  var normalized = directiveNormalize('ng-' + attrName);
  ngAttributeAliasDirectives[normalized] = function() {
    return {
      priority: 99, // it needs to run after the attributes are interpolated
      link: function(scope, element, attr) {
        var propName = attrName,
            name = attrName;

        if (attrName === 'href' &&
            toString.call(element.prop('href')) === '[object SVGAnimatedString]') {
          name = 'xlinkHref';
          attr.$attr[name] = 'xlink:href';
          propName = null;
        }

        attr.$observe(normalized, function(value) {
          if (!value) {
            if (attrName === 'href') {
              attr.$set(name, null);
            }
            return;
          }

          attr.$set(name, value);

          // Support: IE 9-11 only
          // On IE, if "ng:src" directive declaration is used and "src" attribute doesn't exist
          // then calling element.setAttribute('src', 'foo') doesn't do anything, so we need
          // to set the property as well to achieve the desired effect.
          // We use attr[attrName] value since $set can sanitize the url.
          if (msie && propName) element.prop(propName, attr[name]);
        });
      }
    };
  };
});

/* global -nullFormCtrl, -PENDING_CLASS, -SUBMITTED_CLASS
 */
var nullFormCtrl = {
  $addControl: noop,
  $$renameControl: nullFormRenameControl,
  $removeControl: noop,
  $setValidity: noop,
  $setDirty: noop,
  $setPristine: noop,
  $setSubmitted: noop
},
PENDING_CLASS = 'ng-pending',
SUBMITTED_CLASS = 'ng-submitted';

function nullFormRenameControl(control, name) {
  control.$name = name;
}

/**
 * @ngdoc type
 * @name form.FormController
 *
 * @property {boolean} $pristine True if user has not interacted with the form yet.
 * @property {boolean} $dirty True if user has already interacted with the form.
 * @property {boolean} $valid True if all of the containing forms and controls are valid.
 * @property {boolean} $invalid True if at least one containing control or form is invalid.
 * @property {boolean} $pending True if at least one containing control or form is pending.
 * @property {boolean} $submitted True if user has submitted the form even if its invalid.
 *
 * @property {Object} $error Is an object hash, containing references to controls or
 *  forms with failing validators, where:
 *
 *  - keys are validation tokens (error names),
 *  - values are arrays of controls or forms that have a failing validator for given error name.
 *
 *  Built-in validation tokens:
 *
 *  - `email`
 *  - `max`
 *  - `maxlength`
 *  - `min`
 *  - `minlength`
 *  - `number`
 *  - `pattern`
 *  - `required`
 *  - `url`
 *  - `date`
 *  - `datetimelocal`
 *  - `time`
 *  - `week`
 *  - `month`
 *
 * @description
 * `FormController` keeps track of all its controls and nested forms as well as the state of them,
 * such as being valid/invalid or dirty/pristine.
 *
 * Each {@link ng.directive:form form} directive creates an instance
 * of `FormController`.
 *
 */
//asks for $scope to fool the BC controller module
FormController.$inject = ['$element', '$attrs', '$scope', '$animate', '$interpolate'];
function FormController($element, $attrs, $scope, $animate, $interpolate) {
  this.$$controls = [];

  // init state
  this.$error = {};
  this.$$success = {};
  this.$pending = undefined;
  this.$name = $interpolate($attrs.name || $attrs.ngForm || '')($scope);
  this.$dirty = false;
  this.$pristine = true;
  this.$valid = true;
  this.$invalid = false;
  this.$submitted = false;
  this.$$parentForm = nullFormCtrl;

  this.$$element = $element;
  this.$$animate = $animate;

  setupValidity(this);
}

FormController.prototype = {
  /**
   * @ngdoc method
   * @name form.FormController#$rollbackViewValue
   *
   * @description
   * Rollback all form controls pending updates to the `$modelValue`.
   *
   * Updates may be pending by a debounced event or because the input is waiting for a some future
   * event defined in `ng-model-options`. This method is typically needed by the reset button of
   * a form that uses `ng-model-options` to pend updates.
   */
  $rollbackViewValue: function() {
    forEach(this.$$controls, function(control) {
      control.$rollbackViewValue();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$commitViewValue
   *
   * @description
   * Commit all form controls pending updates to the `$modelValue`.
   *
   * Updates may be pending by a debounced event or because the input is waiting for a some future
   * event defined in `ng-model-options`. This method is rarely needed as `NgModelController`
   * usually handles calling this in response to input events.
   */
  $commitViewValue: function() {
    forEach(this.$$controls, function(control) {
      control.$commitViewValue();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$addControl
   * @param {object} control control object, either a {@link form.FormController} or an
   * {@link ngModel.NgModelController}
   *
   * @description
   * Register a control with the form. Input elements using ngModelController do this automatically
   * when they are linked.
   *
   * Note that the current state of the control will not be reflected on the new parent form. This
   * is not an issue with normal use, as freshly compiled and linked controls are in a `$pristine`
   * state.
   *
   * However, if the method is used programmatically, for example by adding dynamically created controls,
   * or controls that have been previously removed without destroying their corresponding DOM element,
   * it's the developers responsibility to make sure the current state propagates to the parent form.
   *
   * For example, if an input control is added that is already `$dirty` and has `$error` properties,
   * calling `$setDirty()` and `$validate()` afterwards will propagate the state to the parent form.
   */
  $addControl: function(control) {
    // Breaking change - before, inputs whose name was "hasOwnProperty" were quietly ignored
    // and not added to the scope.  Now we throw an error.
    assertNotHasOwnProperty(control.$name, 'input');
    this.$$controls.push(control);

    if (control.$name) {
      this[control.$name] = control;
    }

    control.$$parentForm = this;
  },

  // Private API: rename a form control
  $$renameControl: function(control, newName) {
    var oldName = control.$name;

    if (this[oldName] === control) {
      delete this[oldName];
    }
    this[newName] = control;
    control.$name = newName;
  },

  /**
   * @ngdoc method
   * @name form.FormController#$removeControl
   * @param {object} control control object, either a {@link form.FormController} or an
   * {@link ngModel.NgModelController}
   *
   * @description
   * Deregister a control from the form.
   *
   * Input elements using ngModelController do this automatically when they are destroyed.
   *
   * Note that only the removed control's validation state (`$errors`etc.) will be removed from the
   * form. `$dirty`, `$submitted` states will not be changed, because the expected behavior can be
   * different from case to case. For example, removing the only `$dirty` control from a form may or
   * may not mean that the form is still `$dirty`.
   */
  $removeControl: function(control) {
    if (control.$name && this[control.$name] === control) {
      delete this[control.$name];
    }
    forEach(this.$pending, function(value, name) {
      // eslint-disable-next-line no-invalid-this
      this.$setValidity(name, null, control);
    }, this);
    forEach(this.$error, function(value, name) {
      // eslint-disable-next-line no-invalid-this
      this.$setValidity(name, null, control);
    }, this);
    forEach(this.$$success, function(value, name) {
      // eslint-disable-next-line no-invalid-this
      this.$setValidity(name, null, control);
    }, this);

    arrayRemove(this.$$controls, control);
    control.$$parentForm = nullFormCtrl;
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setDirty
   *
   * @description
   * Sets the form to a dirty state.
   *
   * This method can be called to add the 'ng-dirty' class and set the form to a dirty
   * state (ng-dirty class). This method will also propagate to parent forms.
   */
  $setDirty: function() {
    this.$$animate.removeClass(this.$$element, PRISTINE_CLASS);
    this.$$animate.addClass(this.$$element, DIRTY_CLASS);
    this.$dirty = true;
    this.$pristine = false;
    this.$$parentForm.$setDirty();
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setPristine
   *
   * @description
   * Sets the form to its pristine state.
   *
   * This method sets the form's `$pristine` state to true, the `$dirty` state to false, removes
   * the `ng-dirty` class and adds the `ng-pristine` class. Additionally, it sets the `$submitted`
   * state to false.
   *
   * This method will also propagate to all the controls contained in this form.
   *
   * Setting a form back to a pristine state is often useful when we want to 'reuse' a form after
   * saving or resetting it.
   */
  $setPristine: function() {
    this.$$animate.setClass(this.$$element, PRISTINE_CLASS, DIRTY_CLASS + ' ' + SUBMITTED_CLASS);
    this.$dirty = false;
    this.$pristine = true;
    this.$submitted = false;
    forEach(this.$$controls, function(control) {
      control.$setPristine();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setUntouched
   *
   * @description
   * Sets the form to its untouched state.
   *
   * This method can be called to remove the 'ng-touched' class and set the form controls to their
   * untouched state (ng-untouched class).
   *
   * Setting a form controls back to their untouched state is often useful when setting the form
   * back to its pristine state.
   */
  $setUntouched: function() {
    forEach(this.$$controls, function(control) {
      control.$setUntouched();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setSubmitted
   *
   * @description
   * Sets the form to its submitted state.
   */
  $setSubmitted: function() {
    this.$$animate.addClass(this.$$element, SUBMITTED_CLASS);
    this.$submitted = true;
    this.$$parentForm.$setSubmitted();
  }
};

/**
 * @ngdoc method
 * @name form.FormController#$setValidity
 *
 * @description
 * Sets the validity of a form control.
 *
 * This method will also propagate to parent forms.
 */
addSetValidityMethod({
  clazz: FormController,
  set: function(object, property, controller) {
    var list = object[property];
    if (!list) {
      object[property] = [controller];
    } else {
      var index = list.indexOf(controller);
      if (index === -1) {
        list.push(controller);
      }
    }
  },
  unset: function(object, property, controller) {
    var list = object[property];
    if (!list) {
      return;
    }
    arrayRemove(list, controller);
    if (list.length === 0) {
      delete object[property];
    }
  }
});

/**
 * @ngdoc directive
 * @name ngForm
 * @restrict EAC
 *
 * @description
 * Nestable alias of {@link ng.directive:form `form`} directive. HTML
 * does not allow nesting of form elements. It is useful to nest forms, for example if the validity of a
 * sub-group of controls needs to be determined.
 *
 * Note: the purpose of `ngForm` is to group controls,
 * but not to be a replacement for the `<form>` tag with all of its capabilities
 * (e.g. posting to the server, ...).
 *
 * @param {string=} ngForm|name Name of the form. If specified, the form controller will be published into
 *                       related scope, under this name.
 *
 */

 /**
 * @ngdoc directive
 * @name form
 * @restrict E
 *
 * @description
 * Directive that instantiates
 * {@link form.FormController FormController}.
 *
 * If the `name` attribute is specified, the form controller is published onto the current scope under
 * this name.
 *
 * # Alias: {@link ng.directive:ngForm `ngForm`}
 *
 * In Angular, forms can be nested. This means that the outer form is valid when all of the child
 * forms are valid as well. However, browsers do not allow nesting of `<form>` elements, so
 * Angular provides the {@link ng.directive:ngForm `ngForm`} directive, which behaves identically to
 * `form` but can be nested. Nested forms can be useful, for example, if the validity of a sub-group
 * of controls needs to be determined.
 *
 * # CSS classes
 *  - `ng-valid` is set if the form is valid.
 *  - `ng-invalid` is set if the form is invalid.
 *  - `ng-pending` is set if the form is pending.
 *  - `ng-pristine` is set if the form is pristine.
 *  - `ng-dirty` is set if the form is dirty.
 *  - `ng-submitted` is set if the form was submitted.
 *
 * Keep in mind that ngAnimate can detect each of these classes when added and removed.
 *
 *
 * # Submitting a form and preventing the default action
 *
 * Since the role of forms in client-side Angular applications is different than in classical
 * roundtrip apps, it is desirable for the browser not to translate the form submission into a full
 * page reload that sends the data to the server. Instead some javascript logic should be triggered
 * to handle the form submission in an application-specific way.
 *
 * For this reason, Angular prevents the default action (form submission to the server) unless the
 * `<form>` element has an `action` attribute specified.
 *
 * You can use one of the following two ways to specify what javascript method should be called when
 * a form is submitted:
 *
 * - {@link ng.directive:ngSubmit ngSubmit} directive on the form element
 * - {@link ng.directive:ngClick ngClick} directive on the first
  *  button or input field of type submit (input[type=submit])
 *
 * To prevent double execution of the handler, use only one of the {@link ng.directive:ngSubmit ngSubmit}
 * or {@link ng.directive:ngClick ngClick} directives.
 * This is because of the following form submission rules in the HTML specification:
 *
 * - If a form has only one input field then hitting enter in this field triggers form submit
 * (`ngSubmit`)
 * - if a form has 2+ input fields and no buttons or input[type=submit] then hitting enter
 * doesn't trigger submit
 * - if a form has one or more input fields and one or more buttons or input[type=submit] then
 * hitting enter in any of the input fields will trigger the click handler on the *first* button or
 * input[type=submit] (`ngClick`) *and* a submit handler on the enclosing form (`ngSubmit`)
 *
 * Any pending `ngModelOptions` changes will take place immediately when an enclosing form is
 * submitted. Note that `ngClick` events will occur before the model is updated. Use `ngSubmit`
 * to have access to the updated model.
 *
 * ## Animation Hooks
 *
 * Animations in ngForm are triggered when any of the associated CSS classes are added and removed.
 * These classes are: `.ng-pristine`, `.ng-dirty`, `.ng-invalid` and `.ng-valid` as well as any
 * other validations that are performed within the form. Animations in ngForm are similar to how
 * they work in ngClass and animations can be hooked into using CSS transitions, keyframes as well
 * as JS animations.
 *
 * The following example shows a simple way to utilize CSS transitions to style a form element
 * that has been rendered as invalid after it has been validated:
 *
 * <pre>
 * //be sure to include ngAnimate as a module to hook into more
 * //advanced animations
 * .my-form {
 *   transition:0.5s linear all;
 *   background: white;
 * }
 * .my-form.ng-invalid {
 *   background: red;
 *   color:white;
 * }
 * </pre>
 *
 * @example
    <example name="ng-form" deps="angular-animate.js" animations="true" fixBase="true" module="formExample">
      <file name="index.html">
       <script>
         angular.module('formExample', [])
           .controller('FormController', ['$scope', function($scope) {
             $scope.userType = 'guest';
           }]);
       </script>
       <style>
        .my-form {
          transition:all linear 0.5s;
          background: transparent;
        }
        .my-form.ng-invalid {
          background: red;
        }
       </style>
       <form name="myForm" ng-controller="FormController" class="my-form">
         userType: <input name="input" ng-model="userType" required>
         <span class="error" ng-show="myForm.input.$error.required">Required!</span><br>
         <code>userType = {{userType}}</code><br>
         <code>myForm.input.$valid = {{myForm.input.$valid}}</code><br>
         <code>myForm.input.$error = {{myForm.input.$error}}</code><br>
         <code>myForm.$valid = {{myForm.$valid}}</code><br>
         <code>myForm.$error.required = {{!!myForm.$error.required}}</code><br>
        </form>
      </file>
      <file name="protractor.js" type="protractor">
        it('should initialize to model', function() {
          var userType = element(by.binding('userType'));
          var valid = element(by.binding('myForm.input.$valid'));

          expect(userType.getText()).toContain('guest');
          expect(valid.getText()).toContain('true');
        });

        it('should be invalid if empty', function() {
          var userType = element(by.binding('userType'));
          var valid = element(by.binding('myForm.input.$valid'));
          var userInput = element(by.model('userType'));

          userInput.clear();
          userInput.sendKeys('');

          expect(userType.getText()).toEqual('userType =');
          expect(valid.getText()).toContain('false');
        });
      </file>
    </example>
 *
 * @param {string=} name Name of the form. If specified, the form controller will be published into
 *                       related scope, under this name.
 */
var formDirectiveFactory = function(isNgForm) {
  return ['$timeout', '$parse', function($timeout, $parse) {
    var formDirective = {
      name: 'form',
      restrict: isNgForm ? 'EAC' : 'E',
      require: ['form', '^^?form'], //first is the form's own ctrl, second is an optional parent form
      controller: FormController,
      compile: function ngFormCompile(formElement, attr) {
        // Setup initial state of the control
        formElement.addClass(PRISTINE_CLASS).addClass(VALID_CLASS);

        var nameAttr = attr.name ? 'name' : (isNgForm && attr.ngForm ? 'ngForm' : false);

        return {
          pre: function ngFormPreLink(scope, formElement, attr, ctrls) {
            var controller = ctrls[0];

            // if `action` attr is not present on the form, prevent the default action (submission)
            if (!('action' in attr)) {
              // we can't use jq events because if a form is destroyed during submission the default
              // action is not prevented. see #1238
              //
              // IE 9 is not affected because it doesn't fire a submit event and try to do a full
              // page reload if the form was destroyed by submission of the form via a click handler
              // on a button in the form. Looks like an IE9 specific bug.
              var handleFormSubmission = function(event) {
                scope.$apply(function() {
                  controller.$commitViewValue();
                  controller.$setSubmitted();
                });

                event.preventDefault();
              };

              formElement[0].addEventListener('submit', handleFormSubmission);

              // unregister the preventDefault listener so that we don't not leak memory but in a
              // way that will achieve the prevention of the default action.
              formElement.on('$destroy', function() {
                $timeout(function() {
                  formElement[0].removeEventListener('submit', handleFormSubmission);
                }, 0, false);
              });
            }

            var parentFormCtrl = ctrls[1] || controller.$$parentForm;
            parentFormCtrl.$addControl(controller);

            var setter = nameAttr ? getSetter(controller.$name) : noop;

            if (nameAttr) {
              setter(scope, controller);
              attr.$observe(nameAttr, function(newValue) {
                if (controller.$name === newValue) return;
                setter(scope, undefined);
                controller.$$parentForm.$$renameControl(controller, newValue);
                setter = getSetter(controller.$name);
                setter(scope, controller);
              });
            }
            formElement.on('$destroy', function() {
              controller.$$parentForm.$removeControl(controller);
              setter(scope, undefined);
              extend(controller, nullFormCtrl); //stop propagating child destruction handlers upwards
            });
          }
        };
      }
    };

    return formDirective;

    function getSetter(expression) {
      if (expression === '') {
        //create an assignable expression, so forms with an empty name can be renamed later
        return $parse('this[""]').assign;
      }
      return $parse(expression).assign || noop;
    }
  }];
};

var formDirective = formDirectiveFactory();
var ngFormDirective = formDirectiveFactory(true);



// helper methods
function setupValidity(instance) {
  instance.$$classCache = {};
  instance.$$classCache[INVALID_CLASS] = !(instance.$$classCache[VALID_CLASS] = instance.$$element.hasClass(VALID_CLASS));
}
function addSetValidityMethod(context) {
  var clazz = context.clazz,
      set = context.set,
      unset = context.unset;

  clazz.prototype.$setValidity = function(validationErrorKey, state, controller) {
    if (isUndefined(state)) {
      createAndSet(this, '$pending', validationErrorKey, controller);
    } else {
      unsetAndCleanup(this, '$pending', validationErrorKey, controller);
    }
    if (!isBoolean(state)) {
      unset(this.$error, validationErrorKey, controller);
      unset(this.$$success, validationErrorKey, controller);
    } else {
      if (state) {
        unset(this.$error, validationErrorKey, controller);
        set(this.$$success, validationErrorKey, controller);
      } else {
        set(this.$error, validationErrorKey, controller);
        unset(this.$$success, validationErrorKey, controller);
      }
    }
    if (this.$pending) {
      cachedToggleClass(this, PENDING_CLASS, true);
      this.$valid = this.$invalid = undefined;
      toggleValidationCss(this, '', null);
    } else {
      cachedToggleClass(this, PENDING_CLASS, false);
      this.$valid = isObjectEmpty(this.$error);
      this.$invalid = !this.$valid;
      toggleValidationCss(this, '', this.$valid);
    }

    // re-read the state as the set/unset methods could have
    // combined state in this.$error[validationError] (used for forms),
    // where setting/unsetting only increments/decrements the value,
    // and does not replace it.
    var combinedState;
    if (this.$pending && this.$pending[validationErrorKey]) {
      combinedState = undefined;
    } else if (this.$error[validationErrorKey]) {
      combinedState = false;
    } else if (this.$$success[validationErrorKey]) {
      combinedState = true;
    } else {
      combinedState = null;
    }

    toggleValidationCss(this, validationErrorKey, combinedState);
    this.$$parentForm.$setValidity(validationErrorKey, combinedState, this);
  };

  function createAndSet(ctrl, name, value, controller) {
    if (!ctrl[name]) {
      ctrl[name] = {};
    }
    set(ctrl[name], value, controller);
  }

  function unsetAndCleanup(ctrl, name, value, controller) {
    if (ctrl[name]) {
      unset(ctrl[name], value, controller);
    }
    if (isObjectEmpty(ctrl[name])) {
      ctrl[name] = undefined;
    }
  }

  function cachedToggleClass(ctrl, className, switchValue) {
    if (switchValue && !ctrl.$$classCache[className]) {
      ctrl.$$animate.addClass(ctrl.$$element, className);
      ctrl.$$classCache[className] = true;
    } else if (!switchValue && ctrl.$$classCache[className]) {
      ctrl.$$animate.removeClass(ctrl.$$element, className);
      ctrl.$$classCache[className] = false;
    }
  }

  function toggleValidationCss(ctrl, validationErrorKey, isValid) {
    validationErrorKey = validationErrorKey ? '-' + snake_case(validationErrorKey, '-') : '';

    cachedToggleClass(ctrl, VALID_CLASS + validationErrorKey, isValid === true);
    cachedToggleClass(ctrl, INVALID_CLASS + validationErrorKey, isValid === false);
  }
}

function isObjectEmpty(obj) {
  if (obj) {
    for (var prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false;
      }
    }
  }
  return true;
}

/* global
  VALID_CLASS: false,
  INVALID_CLASS: false,
  PRISTINE_CLASS: false,
  DIRTY_CLASS: false,
  ngModelMinErr: false
*/

// Regex code was initially obtained from SO prior to modification: https://stackoverflow.com/questions/3143070/javascript-regex-iso-datetime#answer-3143231
var ISO_DATE_REGEXP = /^\d{4,}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+(?:[+-][0-2]\d:[0-5]\d|Z)$/;
// See valid URLs in RFC3987 (http://tools.ietf.org/html/rfc3987)
// Note: We are being more lenient, because browsers are too.
//   1. Scheme
//   2. Slashes
//   3. Username
//   4. Password
//   5. Hostname
//   6. Port
//   7. Path
//   8. Query
//   9. Fragment
//                 1111111111111111 222   333333    44444        55555555555555555555555     666     77777777     8888888     999
var URL_REGEXP = /^[a-z][a-z\d.+-]*:\/*(?:[^:@]+(?::[^@]+)?@)?(?:[^\s:/?#]+|\[[a-f\d:]+])(?::\d+)?(?:\/[^?#]*)?(?:\?[^#]*)?(?:#.*)?$/i;
// eslint-disable-next-line max-len
var EMAIL_REGEXP = /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
var NUMBER_REGEXP = /^\s*(-|\+)?(\d+|(\d*(\.\d*)))([eE][+-]?\d+)?\s*$/;
var DATE_REGEXP = /^(\d{4,})-(\d{2})-(\d{2})$/;
var DATETIMELOCAL_REGEXP = /^(\d{4,})-(\d\d)-(\d\d)T(\d\d):(\d\d)(?::(\d\d)(\.\d{1,3})?)?$/;
var WEEK_REGEXP = /^(\d{4,})-W(\d\d)$/;
var MONTH_REGEXP = /^(\d{4,})-(\d\d)$/;
var TIME_REGEXP = /^(\d\d):(\d\d)(?::(\d\d)(\.\d{1,3})?)?$/;

var PARTIAL_VALIDATION_EVENTS = 'keydown wheel mousedown';
var PARTIAL_VALIDATION_TYPES = createMap();
forEach('date,datetime-local,month,time,week'.split(','), function(type) {
  PARTIAL_VALIDATION_TYPES[type] = true;
});

var inputType = {

  /**
   * @ngdoc input
   * @name input[text]
   *
   * @description
   * Standard HTML text input with angular data binding, inherited by most of the `input` elements.
   *
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} required Adds `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the Angular expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   * @param {boolean=} [ngTrim=true] If set to false Angular will not automatically trim the input.
   *    This parameter is ignored for input[type=password] controls, which will never trim the
   *    input.
   *
   * @example
      <example name="text-input-directive" module="textInputExample">
        <file name="index.html">
         <script>
           angular.module('textInputExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.example = {
                 text: 'guest',
                 word: /^\s*\w*\s*$/
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>Single word:
             <input type="text" name="input" ng-model="example.text"
                    ng-pattern="example.word" required ng-trim="false">
           </label>
           <div role="alert">
             <span class="error" ng-show="myForm.input.$error.required">
               Required!</span>
             <span class="error" ng-show="myForm.input.$error.pattern">
               Single word only!</span>
           </div>
           <code>text = {{example.text}}</code><br/>
           <code>myForm.input.$valid = {{myForm.input.$valid}}</code><br/>
           <code>myForm.input.$error = {{myForm.input.$error}}</code><br/>
           <code>myForm.$valid = {{myForm.$valid}}</code><br/>
           <code>myForm.$error.required = {{!!myForm.$error.required}}</code><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          var text = element(by.binding('example.text'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('example.text'));

          it('should initialize to model', function() {
            expect(text.getText()).toContain('guest');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');

            expect(text.getText()).toEqual('text =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if multi word', function() {
            input.clear();
            input.sendKeys('hello world');

            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'text': textInputType,

    /**
     * @ngdoc input
     * @name input[date]
     *
     * @description
     * Input with date validation and transformation. In browsers that do not yet support
     * the HTML5 date input, a text element will be used. In that case, text must be entered in a valid ISO-8601
     * date format (yyyy-MM-dd), for example: `2009-01-06`. Since many
     * modern browsers do not yet support this input type, it is important to provide cues to users on the
     * expected input format via a placeholder or label.
     *
     * The model must always be a Date object, otherwise Angular will throw an error.
     * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
     *
     * The timezone to be used to read/write the `Date` instance in the model can be defined using
     * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
     *
     * @param {string} ngModel Assignable angular expression to data-bind to.
     * @param {string=} name Property name of the form under which the control is published.
     * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`. This must be a
     *   valid ISO date string (yyyy-MM-dd). You can also use interpolation inside this attribute
     *   (e.g. `min="{{minDate | date:'yyyy-MM-dd'}}"`). Note that `min` will also add native HTML5
     *   constraint validation.
     * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`. This must be
     *   a valid ISO date string (yyyy-MM-dd). You can also use interpolation inside this attribute
     *   (e.g. `max="{{maxDate | date:'yyyy-MM-dd'}}"`). Note that `max` will also add native HTML5
     *   constraint validation.
     * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO date string
     *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
     * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO date string
     *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
     * @param {string=} required Sets `required` validation error key if the value is not entered.
     * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
     *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
     *    `required` when you want to data-bind to the `required` attribute.
     * @param {string=} ngChange Angular expression to be executed when input changes due to user
     *    interaction with the input element.
     *
     * @example
     <example name="date-input-directive" module="dateInputExample">
     <file name="index.html">
       <script>
          angular.module('dateInputExample', [])
            .controller('DateController', ['$scope', function($scope) {
              $scope.example = {
                value: new Date(2013, 9, 22)
              };
            }]);
       </script>
       <form name="myForm" ng-controller="DateController as dateCtrl">
          <label for="exampleInput">Pick a date in 2013:</label>
          <input type="date" id="exampleInput" name="input" ng-model="example.value"
              placeholder="yyyy-MM-dd" min="2013-01-01" max="2013-12-31" required />
          <div role="alert">
            <span class="error" ng-show="myForm.input.$error.required">
                Required!</span>
            <span class="error" ng-show="myForm.input.$error.date">
                Not a valid date!</span>
           </div>
           <tt>value = {{example.value | date: "yyyy-MM-dd"}}</tt><br/>
           <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
           <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
           <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
           <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
       </form>
     </file>
     <file name="protractor.js" type="protractor">
        var value = element(by.binding('example.value | date: "yyyy-MM-dd"'));
        var valid = element(by.binding('myForm.input.$valid'));

        // currently protractor/webdriver does not support
        // sending keys to all known HTML5 input controls
        // for various browsers (see https://github.com/angular/protractor/issues/562).
        function setInput(val) {
          // set the value of the element and force validation.
          var scr = "var ipt = document.getElementById('exampleInput'); " +
          "ipt.value = '" + val + "';" +
          "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
          browser.executeScript(scr);
        }

        it('should initialize to model', function() {
          expect(value.getText()).toContain('2013-10-22');
          expect(valid.getText()).toContain('myForm.input.$valid = true');
        });

        it('should be invalid if empty', function() {
          setInput('');
          expect(value.getText()).toEqual('value =');
          expect(valid.getText()).toContain('myForm.input.$valid = false');
        });

        it('should be invalid if over max', function() {
          setInput('2015-01-01');
          expect(value.getText()).toContain('');
          expect(valid.getText()).toContain('myForm.input.$valid = false');
        });
     </file>
     </example>
     */
  'date': createDateInputType('date', DATE_REGEXP,
         createDateParser(DATE_REGEXP, ['yyyy', 'MM', 'dd']),
         'yyyy-MM-dd'),

   /**
    * @ngdoc input
    * @name input[datetime-local]
    *
    * @description
    * Input with datetime validation and transformation. In browsers that do not yet support
    * the HTML5 date input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
    * local datetime format (yyyy-MM-ddTHH:mm:ss), for example: `2010-12-28T14:57:00`.
    *
    * The model must always be a Date object, otherwise Angular will throw an error.
    * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
    *
    * The timezone to be used to read/write the `Date` instance in the model can be defined using
    * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
    *
    * @param {string} ngModel Assignable angular expression to data-bind to.
    * @param {string=} name Property name of the form under which the control is published.
    * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
    *   This must be a valid ISO datetime format (yyyy-MM-ddTHH:mm:ss). You can also use interpolation
    *   inside this attribute (e.g. `min="{{minDatetimeLocal | date:'yyyy-MM-ddTHH:mm:ss'}}"`).
    *   Note that `min` will also add native HTML5 constraint validation.
    * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
    *   This must be a valid ISO datetime format (yyyy-MM-ddTHH:mm:ss). You can also use interpolation
    *   inside this attribute (e.g. `max="{{maxDatetimeLocal | date:'yyyy-MM-ddTHH:mm:ss'}}"`).
    *   Note that `max` will also add native HTML5 constraint validation.
    * @param {(date|string)=} ngMin Sets the `min` validation error key to the Date / ISO datetime string
    *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
    * @param {(date|string)=} ngMax Sets the `max` validation error key to the Date / ISO datetime string
    *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
    * @param {string=} required Sets `required` validation error key if the value is not entered.
    * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
    *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
    *    `required` when you want to data-bind to the `required` attribute.
    * @param {string=} ngChange Angular expression to be executed when input changes due to user
    *    interaction with the input element.
    *
    * @example
    <example name="datetimelocal-input-directive" module="dateExample">
    <file name="index.html">
      <script>
        angular.module('dateExample', [])
          .controller('DateController', ['$scope', function($scope) {
            $scope.example = {
              value: new Date(2010, 11, 28, 14, 57)
            };
          }]);
      </script>
      <form name="myForm" ng-controller="DateController as dateCtrl">
        <label for="exampleInput">Pick a date between in 2013:</label>
        <input type="datetime-local" id="exampleInput" name="input" ng-model="example.value"
            placeholder="yyyy-MM-ddTHH:mm:ss" min="2001-01-01T00:00:00" max="2013-12-31T00:00:00" required />
        <div role="alert">
          <span class="error" ng-show="myForm.input.$error.required">
              Required!</span>
          <span class="error" ng-show="myForm.input.$error.datetimelocal">
              Not a valid date!</span>
        </div>
        <tt>value = {{example.value | date: "yyyy-MM-ddTHH:mm:ss"}}</tt><br/>
        <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
        <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
        <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
        <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
      </form>
    </file>
    <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "yyyy-MM-ddTHH:mm:ss"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('2010-12-28T14:57:00');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('2015-01-01T23:59:00');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
    </file>
    </example>
    */
  'datetime-local': createDateInputType('datetimelocal', DATETIMELOCAL_REGEXP,
      createDateParser(DATETIMELOCAL_REGEXP, ['yyyy', 'MM', 'dd', 'HH', 'mm', 'ss', 'sss']),
      'yyyy-MM-ddTHH:mm:ss.sss'),

  /**
   * @ngdoc input
   * @name input[time]
   *
   * @description
   * Input with time validation and transformation. In browsers that do not yet support
   * the HTML5 time input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
   * local time format (HH:mm:ss), for example: `14:57:00`. Model must be a Date object. This binding will always output a
   * Date object to the model of January 1, 1970, or local date `new Date(1970, 0, 1, HH, mm, ss)`.
   *
   * The model must always be a Date object, otherwise Angular will throw an error.
   * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
   *
   * The timezone to be used to read/write the `Date` instance in the model can be defined using
   * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
   *   This must be a valid ISO time format (HH:mm:ss). You can also use interpolation inside this
   *   attribute (e.g. `min="{{minTime | date:'HH:mm:ss'}}"`). Note that `min` will also add
   *   native HTML5 constraint validation.
   * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
   *   This must be a valid ISO time format (HH:mm:ss). You can also use interpolation inside this
   *   attribute (e.g. `max="{{maxTime | date:'HH:mm:ss'}}"`). Note that `max` will also add
   *   native HTML5 constraint validation.
   * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO time string the
   *   `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
   * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO time string the
   *   `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
   <example name="time-input-directive" module="timeExample">
   <file name="index.html">
     <script>
      angular.module('timeExample', [])
        .controller('DateController', ['$scope', function($scope) {
          $scope.example = {
            value: new Date(1970, 0, 1, 14, 57, 0)
          };
        }]);
     </script>
     <form name="myForm" ng-controller="DateController as dateCtrl">
        <label for="exampleInput">Pick a time between 8am and 5pm:</label>
        <input type="time" id="exampleInput" name="input" ng-model="example.value"
            placeholder="HH:mm:ss" min="08:00:00" max="17:00:00" required />
        <div role="alert">
          <span class="error" ng-show="myForm.input.$error.required">
              Required!</span>
          <span class="error" ng-show="myForm.input.$error.time">
              Not a valid date!</span>
        </div>
        <tt>value = {{example.value | date: "HH:mm:ss"}}</tt><br/>
        <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
        <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
        <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
        <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
     </form>
   </file>
   <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "HH:mm:ss"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('14:57:00');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('23:59:00');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
   </file>
   </example>
   */
  'time': createDateInputType('time', TIME_REGEXP,
      createDateParser(TIME_REGEXP, ['HH', 'mm', 'ss', 'sss']),
     'HH:mm:ss.sss'),

   /**
    * @ngdoc input
    * @name input[week]
    *
    * @description
    * Input with week-of-the-year validation and transformation to Date. In browsers that do not yet support
    * the HTML5 week input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
    * week format (yyyy-W##), for example: `2013-W02`.
    *
    * The model must always be a Date object, otherwise Angular will throw an error.
    * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
    *
    * The timezone to be used to read/write the `Date` instance in the model can be defined using
    * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
    *
    * @param {string} ngModel Assignable angular expression to data-bind to.
    * @param {string=} name Property name of the form under which the control is published.
    * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
    *   This must be a valid ISO week format (yyyy-W##). You can also use interpolation inside this
    *   attribute (e.g. `min="{{minWeek | date:'yyyy-Www'}}"`). Note that `min` will also add
    *   native HTML5 constraint validation.
    * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
    *   This must be a valid ISO week format (yyyy-W##). You can also use interpolation inside this
    *   attribute (e.g. `max="{{maxWeek | date:'yyyy-Www'}}"`). Note that `max` will also add
    *   native HTML5 constraint validation.
    * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO week string
    *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
    * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO week string
    *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
    * @param {string=} required Sets `required` validation error key if the value is not entered.
    * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
    *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
    *    `required` when you want to data-bind to the `required` attribute.
    * @param {string=} ngChange Angular expression to be executed when input changes due to user
    *    interaction with the input element.
    *
    * @example
    <example name="week-input-directive" module="weekExample">
    <file name="index.html">
      <script>
      angular.module('weekExample', [])
        .controller('DateController', ['$scope', function($scope) {
          $scope.example = {
            value: new Date(2013, 0, 3)
          };
        }]);
      </script>
      <form name="myForm" ng-controller="DateController as dateCtrl">
        <label>Pick a date between in 2013:
          <input id="exampleInput" type="week" name="input" ng-model="example.value"
                 placeholder="YYYY-W##" min="2012-W32"
                 max="2013-W52" required />
        </label>
        <div role="alert">
          <span class="error" ng-show="myForm.input.$error.required">
              Required!</span>
          <span class="error" ng-show="myForm.input.$error.week">
              Not a valid date!</span>
        </div>
        <tt>value = {{example.value | date: "yyyy-Www"}}</tt><br/>
        <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
        <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
        <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
        <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
      </form>
    </file>
    <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "yyyy-Www"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('2013-W01');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('2015-W01');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
    </file>
    </example>
    */
  'week': createDateInputType('week', WEEK_REGEXP, weekParser, 'yyyy-Www'),

  /**
   * @ngdoc input
   * @name input[month]
   *
   * @description
   * Input with month validation and transformation. In browsers that do not yet support
   * the HTML5 month input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
   * month format (yyyy-MM), for example: `2009-01`.
   *
   * The model must always be a Date object, otherwise Angular will throw an error.
   * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
   * If the model is not set to the first of the month, the next view to model update will set it
   * to the first of the month.
   *
   * The timezone to be used to read/write the `Date` instance in the model can be defined using
   * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
   *   This must be a valid ISO month format (yyyy-MM). You can also use interpolation inside this
   *   attribute (e.g. `min="{{minMonth | date:'yyyy-MM'}}"`). Note that `min` will also add
   *   native HTML5 constraint validation.
   * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
   *   This must be a valid ISO month format (yyyy-MM). You can also use interpolation inside this
   *   attribute (e.g. `max="{{maxMonth | date:'yyyy-MM'}}"`). Note that `max` will also add
   *   native HTML5 constraint validation.
   * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO week string
   *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
   * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO week string
   *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.

   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
   <example name="month-input-directive" module="monthExample">
   <file name="index.html">
     <script>
      angular.module('monthExample', [])
        .controller('DateController', ['$scope', function($scope) {
          $scope.example = {
            value: new Date(2013, 9, 1)
          };
        }]);
     </script>
     <form name="myForm" ng-controller="DateController as dateCtrl">
       <label for="exampleInput">Pick a month in 2013:</label>
       <input id="exampleInput" type="month" name="input" ng-model="example.value"
          placeholder="yyyy-MM" min="2013-01" max="2013-12" required />
       <div role="alert">
         <span class="error" ng-show="myForm.input.$error.required">
            Required!</span>
         <span class="error" ng-show="myForm.input.$error.month">
            Not a valid month!</span>
       </div>
       <tt>value = {{example.value | date: "yyyy-MM"}}</tt><br/>
       <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
       <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
       <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
       <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
     </form>
   </file>
   <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "yyyy-MM"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('2013-10');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('2015-01');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
   </file>
   </example>
   */
  'month': createDateInputType('month', MONTH_REGEXP,
     createDateParser(MONTH_REGEXP, ['yyyy', 'MM']),
     'yyyy-MM'),

  /**
   * @ngdoc input
   * @name input[number]
   *
   * @description
   * Text input with number validation and transformation. Sets the `number` validation
   * error if not a valid number.
   *
   * <div class="alert alert-warning">
   * The model must always be of type `number` otherwise Angular will throw an error.
   * Be aware that a string containing a number is not enough. See the {@link ngModel:numfmt}
   * error docs for more information and an example of how to convert your model if necessary.
   * </div>
   *
   * ## Issues with HTML5 constraint validation
   *
   * In browsers that follow the
   * [HTML5 specification](https://html.spec.whatwg.org/multipage/forms.html#number-state-%28type=number%29),
   * `input[number]` does not work as expected with {@link ngModelOptions `ngModelOptions.allowInvalid`}.
   * If a non-number is entered in the input, the browser will report the value as an empty string,
   * which means the view / model values in `ngModel` and subsequently the scope value
   * will also be an empty string.
   *
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
   *    Can be interpolated.
   * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
   *    Can be interpolated.
   * @param {string=} ngMin Like `min`, sets the `min` validation error key if the value entered is less than `ngMin`,
   *    but does not trigger HTML5 native validation. Takes an expression.
   * @param {string=} ngMax Like `max`, sets the `max` validation error key if the value entered is greater than `ngMax`,
   *    but does not trigger HTML5 native validation. Takes an expression.
   * @param {string=} step Sets the `step` validation error key if the value entered does not fit the `step` constraint.
   *    Can be interpolated.
   * @param {string=} ngStep Like `step`, sets the `step` validation error key if the value entered does not fit the `ngStep` constraint,
   *    but does not trigger HTML5 native validation. Takes an expression.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the Angular expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="number-input-directive" module="numberExample">
        <file name="index.html">
         <script>
           angular.module('numberExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.example = {
                 value: 12
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>Number:
             <input type="number" name="input" ng-model="example.value"
                    min="0" max="99" required>
          </label>
           <div role="alert">
             <span class="error" ng-show="myForm.input.$error.required">
               Required!</span>
             <span class="error" ng-show="myForm.input.$error.number">
               Not valid number!</span>
           </div>
           <tt>value = {{example.value}}</tt><br/>
           <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
           <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
           <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
           <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          var value = element(by.binding('example.value'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('example.value'));

          it('should initialize to model', function() {
            expect(value.getText()).toContain('12');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');
            expect(value.getText()).toEqual('value =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if over max', function() {
            input.clear();
            input.sendKeys('123');
            expect(value.getText()).toEqual('value =');
            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'number': numberInputType,


  /**
   * @ngdoc input
   * @name input[url]
   *
   * @description
   * Text input with URL validation. Sets the `url` validation error key if the content is not a
   * valid URL.
   *
   * <div class="alert alert-warning">
   * **Note:** `input[url]` uses a regex to validate urls that is derived from the regex
   * used in Chromium. If you need stricter validation, you can use `ng-pattern` or modify
   * the built-in validators (see the {@link guide/forms Forms guide})
   * </div>
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the Angular expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="url-input-directive" module="urlExample">
        <file name="index.html">
         <script>
           angular.module('urlExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.url = {
                 text: 'http://google.com'
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>URL:
             <input type="url" name="input" ng-model="url.text" required>
           <label>
           <div role="alert">
             <span class="error" ng-show="myForm.input.$error.required">
               Required!</span>
             <span class="error" ng-show="myForm.input.$error.url">
               Not valid url!</span>
           </div>
           <tt>text = {{url.text}}</tt><br/>
           <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
           <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
           <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
           <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
           <tt>myForm.$error.url = {{!!myForm.$error.url}}</tt><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          var text = element(by.binding('url.text'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('url.text'));

          it('should initialize to model', function() {
            expect(text.getText()).toContain('http://google.com');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');

            expect(text.getText()).toEqual('text =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if not url', function() {
            input.clear();
            input.sendKeys('box');

            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'url': urlInputType,


  /**
   * @ngdoc input
   * @name input[email]
   *
   * @description
   * Text input with email validation. Sets the `email` validation error key if not a valid email
   * address.
   *
   * <div class="alert alert-warning">
   * **Note:** `input[email]` uses a regex to validate email addresses that is derived from the regex
   * used in Chromium. If you need stricter validation (e.g. requiring a top-level domain), you can
   * use `ng-pattern` or modify the built-in validators (see the {@link guide/forms Forms guide})
   * </div>
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the Angular expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="email-input-directive" module="emailExample">
        <file name="index.html">
         <script>
           angular.module('emailExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.email = {
                 text: 'me@example.com'
               };
             }]);
         </script>
           <form name="myForm" ng-controller="ExampleController">
             <label>Email:
               <input type="email" name="input" ng-model="email.text" required>
             </label>
             <div role="alert">
               <span class="error" ng-show="myForm.input.$error.required">
                 Required!</span>
               <span class="error" ng-show="myForm.input.$error.email">
                 Not valid email!</span>
             </div>
             <tt>text = {{email.text}}</tt><br/>
             <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
             <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
             <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
             <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
             <tt>myForm.$error.email = {{!!myForm.$error.email}}</tt><br/>
           </form>
         </file>
        <file name="protractor.js" type="protractor">
          var text = element(by.binding('email.text'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('email.text'));

          it('should initialize to model', function() {
            expect(text.getText()).toContain('me@example.com');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');
            expect(text.getText()).toEqual('text =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if not email', function() {
            input.clear();
            input.sendKeys('xxx');

            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'email': emailInputType,


  /**
   * @ngdoc input
   * @name input[radio]
   *
   * @description
   * HTML radio button.
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string} value The value to which the `ngModel` expression should be set when selected.
   *    Note that `value` only supports `string` values, i.e. the scope model needs to be a string,
   *    too. Use `ngValue` if you need complex models (`number`, `object`, ...).
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   * @param {string} ngValue Angular expression to which `ngModel` will be be set when the radio
   *    is selected. Should be used instead of the `value` attribute if you need
   *    a non-string `ngModel` (`boolean`, `array`, ...).
   *
   * @example
      <example name="radio-input-directive" module="radioExample">
        <file name="index.html">
         <script>
           angular.module('radioExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.color = {
                 name: 'blue'
               };
               $scope.specialValue = {
                 "id": "12345",
                 "value": "green"
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>
             <input type="radio" ng-model="color.name" value="red">
             Red
           </label><br/>
           <label>
             <input type="radio" ng-model="color.name" ng-value="specialValue">
             Green
           </label><br/>
           <label>
             <input type="radio" ng-model="color.name" value="blue">
             Blue
           </label><br/>
           <tt>color = {{color.name | json}}</tt><br/>
          </form>
          Note that `ng-value="specialValue"` sets radio item's value to be the value of `$scope.specialValue`.
        </file>
        <file name="protractor.js" type="protractor">
          it('should change state', function() {
            var inputs = element.all(by.model('color.name'));
            var color = element(by.binding('color.name'));

            expect(color.getText()).toContain('blue');

            inputs.get(0).click();
            expect(color.getText()).toContain('red');

            inputs.get(1).click();
            expect(color.getText()).toContain('green');
          });
        </file>
      </example>
   */
  'radio': radioInputType,

  /**
   * @ngdoc input
   * @name input[range]
   *
   * @description
   * Native range input with validation and transformation.
   *
   * The model for the range input must always be a `Number`.
   *
   * IE9 and other browsers that do not support the `range` type fall back
   * to a text input without any default values for `min`, `max` and `step`. Model binding,
   * validation and number parsing are nevertheless supported.
   *
   * Browsers that support range (latest Chrome, Safari, Firefox, Edge) treat `input[range]`
   * in a way that never allows the input to hold an invalid value. That means:
   * - any non-numerical value is set to `(max + min) / 2`.
   * - any numerical value that is less than the current min val, or greater than the current max val
   * is set to the min / max val respectively.
   * - additionally, the current `step` is respected, so the nearest value that satisfies a step
   * is used.
   *
   * See the [HTML Spec on input[type=range]](https://www.w3.org/TR/html5/forms.html#range-state-(type=range))
   * for more info.
   *
   * This has the following consequences for Angular:
   *
   * Since the element value should always reflect the current model value, a range input
   * will set the bound ngModel expression to the value that the browser has set for the
   * input element. For example, in the following input `<input type="range" ng-model="model.value">`,
   * if the application sets `model.value = null`, the browser will set the input to `'50'`.
   * Angular will then set the model to `50`, to prevent input and model value being out of sync.
   *
   * That means the model for range will immediately be set to `50` after `ngModel` has been
   * initialized. It also means a range input can never have the required error.
   *
   * This does not only affect changes to the model value, but also to the values of the `min`,
   * `max`, and `step` attributes. When these change in a way that will cause the browser to modify
   * the input value, Angular will also update the model value.
   *
   * Automatic value adjustment also means that a range input element can never have the `required`,
   * `min`, or `max` errors.
   *
   * However, `step` is currently only fully implemented by Firefox. Other browsers have problems
   * when the step value changes dynamically - they do not adjust the element value correctly, but
   * instead may set the `stepMismatch` error. If that's the case, the Angular will set the `step`
   * error on the input, and set the model to `undefined`.
   *
   * Note that `input[range]` is not compatible with`ngMax`, `ngMin`, and `ngStep`, because they do
   * not set the `min` and `max` attributes, which means that the browser won't automatically adjust
   * the input value based on their values, and will always assume min = 0, max = 100, and step = 1.
   *
   * @param {string}  ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation to ensure that the value entered is greater
   *                  than `min`. Can be interpolated.
   * @param {string=} max Sets the `max` validation to ensure that the value entered is less than `max`.
   *                  Can be interpolated.
   * @param {string=} step Sets the `step` validation to ensure that the value entered matches the `step`
   *                  Can be interpolated.
   * @param {string=} ngChange Angular expression to be executed when the ngModel value changes due
   *                  to user interaction with the input element.
   * @param {expression=} ngChecked If the expression is truthy, then the `checked` attribute will be set on the
   *                      element. **Note** : `ngChecked` should not be used alongside `ngModel`.
   *                      Checkout {@link ng.directive:ngChecked ngChecked} for usage.
   *
   * @example
      <example name="range-input-directive" module="rangeExample">
        <file name="index.html">
          <script>
            angular.module('rangeExample', [])
              .controller('ExampleController', ['$scope', function($scope) {
                $scope.value = 75;
                $scope.min = 10;
                $scope.max = 90;
              }]);
          </script>
          <form name="myForm" ng-controller="ExampleController">

            Model as range: <input type="range" name="range" ng-model="value" min="{{min}}"  max="{{max}}">
            <hr>
            Model as number: <input type="number" ng-model="value"><br>
            Min: <input type="number" ng-model="min"><br>
            Max: <input type="number" ng-model="max"><br>
            value = <code>{{value}}</code><br/>
            myForm.range.$valid = <code>{{myForm.range.$valid}}</code><br/>
            myForm.range.$error = <code>{{myForm.range.$error}}</code>
          </form>
        </file>
      </example>

   * ## Range Input with ngMin & ngMax attributes

   * @example
      <example name="range-input-directive-ng" module="rangeExample">
        <file name="index.html">
          <script>
            angular.module('rangeExample', [])
              .controller('ExampleController', ['$scope', function($scope) {
                $scope.value = 75;
                $scope.min = 10;
                $scope.max = 90;
              }]);
          </script>
          <form name="myForm" ng-controller="ExampleController">
            Model as range: <input type="range" name="range" ng-model="value" ng-min="min" ng-max="max">
            <hr>
            Model as number: <input type="number" ng-model="value"><br>
            Min: <input type="number" ng-model="min"><br>
            Max: <input type="number" ng-model="max"><br>
            value = <code>{{value}}</code><br/>
            myForm.range.$valid = <code>{{myForm.range.$valid}}</code><br/>
            myForm.range.$error = <code>{{myForm.range.$error}}</code>
          </form>
        </file>
      </example>

   */
  'range': rangeInputType,

  /**
   * @ngdoc input
   * @name input[checkbox]
   *
   * @description
   * HTML checkbox.
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {expression=} ngTrueValue The value to which the expression should be set when selected.
   * @param {expression=} ngFalseValue The value to which the expression should be set when not selected.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="checkbox-input-directive" module="checkboxExample">
        <file name="index.html">
         <script>
           angular.module('checkboxExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.checkboxModel = {
                value1 : true,
                value2 : 'YES'
              };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>Value1:
             <input type="checkbox" ng-model="checkboxModel.value1">
           </label><br/>
           <label>Value2:
             <input type="checkbox" ng-model="checkboxModel.value2"
                    ng-true-value="'YES'" ng-false-value="'NO'">
            </label><br/>
           <tt>value1 = {{checkboxModel.value1}}</tt><br/>
           <tt>value2 = {{checkboxModel.value2}}</tt><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          it('should change state', function() {
            var value1 = element(by.binding('checkboxModel.value1'));
            var value2 = element(by.binding('checkboxModel.value2'));

            expect(value1.getText()).toContain('true');
            expect(value2.getText()).toContain('YES');

            element(by.model('checkboxModel.value1')).click();
            element(by.model('checkboxModel.value2')).click();

            expect(value1.getText()).toContain('false');
            expect(value2.getText()).toContain('NO');
          });
        </file>
      </example>
   */
  'checkbox': checkboxInputType,

  'hidden': noop,
  'button': noop,
  'submit': noop,
  'reset': noop,
  'file': noop
};

function stringBasedInputType(ctrl) {
  ctrl.$formatters.push(function(value) {
    return ctrl.$isEmpty(value) ? value : value.toString();
  });
}

function textInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
  stringBasedInputType(ctrl);
}

function baseInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  var type = lowercase(element[0].type);

  // In composition mode, users are still inputting intermediate text buffer,
  // hold the listener until composition is done.
  // More about composition events: https://developer.mozilla.org/en-US/docs/Web/API/CompositionEvent
  if (!$sniffer.android) {
    var composing = false;

    element.on('compositionstart', function() {
      composing = true;
    });

    element.on('compositionend', function() {
      composing = false;
      listener();
    });
  }

  var timeout;

  var listener = function(ev) {
    if (timeout) {
      $browser.defer.cancel(timeout);
      timeout = null;
    }
    if (composing) return;
    var value = element.val(),
        event = ev && ev.type;

    // By default we will trim the value
    // If the attribute ng-trim exists we will avoid trimming
    // If input type is 'password', the value is never trimmed
    if (type !== 'password' && (!attr.ngTrim || attr.ngTrim !== 'false')) {
      value = trim(value);
    }

    // If a control is suffering from bad input (due to native validators), browsers discard its
    // value, so it may be necessary to revalidate (by calling $setViewValue again) even if the
    // control's value is the same empty value twice in a row.
    if (ctrl.$viewValue !== value || (value === '' && ctrl.$$hasNativeValidators)) {
      ctrl.$setViewValue(value, event);
    }
  };

  // if the browser does support "input" event, we are fine - except on IE9 which doesn't fire the
  // input event on backspace, delete or cut
  if ($sniffer.hasEvent('input')) {
    element.on('input', listener);
  } else {
    var deferListener = function(ev, input, origValue) {
      if (!timeout) {
        timeout = $browser.defer(function() {
          timeout = null;
          if (!input || input.value !== origValue) {
            listener(ev);
          }
        });
      }
    };

    element.on('keydown', /** @this */ function(event) {
      var key = event.keyCode;

      // ignore
      //    command            modifiers                   arrows
      if (key === 91 || (15 < key && key < 19) || (37 <= key && key <= 40)) return;

      deferListener(event, this, this.value);
    });

    // if user modifies input value using context menu in IE, we need "paste" and "cut" events to catch it
    if ($sniffer.hasEvent('paste')) {
      element.on('paste cut', deferListener);
    }
  }

  // if user paste into input using mouse on older browser
  // or form autocomplete on newer browser, we need "change" event to catch it
  element.on('change', listener);

  // Some native input types (date-family) have the ability to change validity without
  // firing any input/change events.
  // For these event types, when native validators are present and the browser supports the type,
  // check for validity changes on various DOM events.
  if (PARTIAL_VALIDATION_TYPES[type] && ctrl.$$hasNativeValidators && type === attr.type) {
    element.on(PARTIAL_VALIDATION_EVENTS, /** @this */ function(ev) {
      if (!timeout) {
        var validity = this[VALIDITY_STATE_PROPERTY];
        var origBadInput = validity.badInput;
        var origTypeMismatch = validity.typeMismatch;
        timeout = $browser.defer(function() {
          timeout = null;
          if (validity.badInput !== origBadInput || validity.typeMismatch !== origTypeMismatch) {
            listener(ev);
          }
        });
      }
    });
  }

  ctrl.$render = function() {
    // Workaround for Firefox validation #12102.
    var value = ctrl.$isEmpty(ctrl.$viewValue) ? '' : ctrl.$viewValue;
    if (element.val() !== value) {
      element.val(value);
    }
  };
}

function weekParser(isoWeek, existingDate) {
  if (isDate(isoWeek)) {
    return isoWeek;
  }

  if (isString(isoWeek)) {
    WEEK_REGEXP.lastIndex = 0;
    var parts = WEEK_REGEXP.exec(isoWeek);
    if (parts) {
      var year = +parts[1],
          week = +parts[2],
          hours = 0,
          minutes = 0,
          seconds = 0,
          milliseconds = 0,
          firstThurs = getFirstThursdayOfYear(year),
          addDays = (week - 1) * 7;

      if (existingDate) {
        hours = existingDate.getHours();
        minutes = existingDate.getMinutes();
        seconds = existingDate.getSeconds();
        milliseconds = existingDate.getMilliseconds();
      }

      return new Date(year, 0, firstThurs.getDate() + addDays, hours, minutes, seconds, milliseconds);
    }
  }

  return NaN;
}

function createDateParser(regexp, mapping) {
  return function(iso, date) {
    var parts, map;

    if (isDate(iso)) {
      return iso;
    }

    if (isString(iso)) {
      // When a date is JSON'ified to wraps itself inside of an extra
      // set of double quotes. This makes the date parsing code unable
      // to match the date string and parse it as a date.
      if (iso.charAt(0) === '"' && iso.charAt(iso.length - 1) === '"') {
        iso = iso.substring(1, iso.length - 1);
      }
      if (ISO_DATE_REGEXP.test(iso)) {
        return new Date(iso);
      }
      regexp.lastIndex = 0;
      parts = regexp.exec(iso);

      if (parts) {
        parts.shift();
        if (date) {
          map = {
            yyyy: date.getFullYear(),
            MM: date.getMonth() + 1,
            dd: date.getDate(),
            HH: date.getHours(),
            mm: date.getMinutes(),
            ss: date.getSeconds(),
            sss: date.getMilliseconds() / 1000
          };
        } else {
          map = { yyyy: 1970, MM: 1, dd: 1, HH: 0, mm: 0, ss: 0, sss: 0 };
        }

        forEach(parts, function(part, index) {
          if (index < mapping.length) {
            map[mapping[index]] = +part;
          }
        });
        return new Date(map.yyyy, map.MM - 1, map.dd, map.HH, map.mm, map.ss || 0, map.sss * 1000 || 0);
      }
    }

    return NaN;
  };
}

function createDateInputType(type, regexp, parseDate, format) {
  return function dynamicDateInputType(scope, element, attr, ctrl, $sniffer, $browser, $filter) {
    badInputChecker(scope, element, attr, ctrl);
    baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
    var timezone = ctrl && ctrl.$options.getOption('timezone');
    var previousDate;

    ctrl.$$parserName = type;
    ctrl.$parsers.push(function(value) {
      if (ctrl.$isEmpty(value)) return null;
      if (regexp.test(value)) {
        // Note: We cannot read ctrl.$modelValue, as there might be a different
        // parser/formatter in the processing chain so that the model
        // contains some different data format!
        var parsedDate = parseDate(value, previousDate);
        if (timezone) {
          parsedDate = convertTimezoneToLocal(parsedDate, timezone);
        }
        return parsedDate;
      }
      return undefined;
    });

    ctrl.$formatters.push(function(value) {
      if (value && !isDate(value)) {
        throw ngModelMinErr('datefmt', 'Expected `{0}` to be a date', value);
      }
      if (isValidDate(value)) {
        previousDate = value;
        if (previousDate && timezone) {
          previousDate = convertTimezoneToLocal(previousDate, timezone, true);
        }
        return $filter('date')(value, format, timezone);
      } else {
        previousDate = null;
        return '';
      }
    });

    if (isDefined(attr.min) || attr.ngMin) {
      var minVal;
      ctrl.$validators.min = function(value) {
        return !isValidDate(value) || isUndefined(minVal) || parseDate(value) >= minVal;
      };
      attr.$observe('min', function(val) {
        minVal = parseObservedDateValue(val);
        ctrl.$validate();
      });
    }

    if (isDefined(attr.max) || attr.ngMax) {
      var maxVal;
      ctrl.$validators.max = function(value) {
        return !isValidDate(value) || isUndefined(maxVal) || parseDate(value) <= maxVal;
      };
      attr.$observe('max', function(val) {
        maxVal = parseObservedDateValue(val);
        ctrl.$validate();
      });
    }

    function isValidDate(value) {
      // Invalid Date: getTime() returns NaN
      return value && !(value.getTime && value.getTime() !== value.getTime());
    }

    function parseObservedDateValue(val) {
      return isDefined(val) && !isDate(val) ? parseDate(val) || undefined : val;
    }
  };
}

function badInputChecker(scope, element, attr, ctrl) {
  var node = element[0];
  var nativeValidation = ctrl.$$hasNativeValidators = isObject(node.validity);
  if (nativeValidation) {
    ctrl.$parsers.push(function(value) {
      var validity = element.prop(VALIDITY_STATE_PROPERTY) || {};
      return validity.badInput || validity.typeMismatch ? undefined : value;
    });
  }
}

function numberFormatterParser(ctrl) {
  ctrl.$$parserName = 'number';
  ctrl.$parsers.push(function(value) {
    if (ctrl.$isEmpty(value))      return null;
    if (NUMBER_REGEXP.test(value)) return parseFloat(value);
    return undefined;
  });

  ctrl.$formatters.push(function(value) {
    if (!ctrl.$isEmpty(value)) {
      if (!isNumber(value)) {
        throw ngModelMinErr('numfmt', 'Expected `{0}` to be a number', value);
      }
      value = value.toString();
    }
    return value;
  });
}

function parseNumberAttrVal(val) {
  if (isDefined(val) && !isNumber(val)) {
    val = parseFloat(val);
  }
  return !isNumberNaN(val) ? val : undefined;
}

function isNumberInteger(num) {
  // See http://stackoverflow.com/questions/14636536/how-to-check-if-a-variable-is-an-integer-in-javascript#14794066
  // (minus the assumption that `num` is a number)

  // eslint-disable-next-line no-bitwise
  return (num | 0) === num;
}

function countDecimals(num) {
  var numString = num.toString();
  var decimalSymbolIndex = numString.indexOf('.');

  if (decimalSymbolIndex === -1) {
    if (-1 < num && num < 1) {
      // It may be in the exponential notation format (`1e-X`)
      var match = /e-(\d+)$/.exec(numString);

      if (match) {
        return Number(match[1]);
      }
    }

    return 0;
  }

  return numString.length - decimalSymbolIndex - 1;
}

function isValidForStep(viewValue, stepBase, step) {
  // At this point `stepBase` and `step` are expected to be non-NaN values
  // and `viewValue` is expected to be a valid stringified number.
  var value = Number(viewValue);

  var isNonIntegerValue = !isNumberInteger(value);
  var isNonIntegerStepBase = !isNumberInteger(stepBase);
  var isNonIntegerStep = !isNumberInteger(step);

  // Due to limitations in Floating Point Arithmetic (e.g. `0.3 - 0.2 !== 0.1` or
  // `0.5 % 0.1 !== 0`), we need to convert all numbers to integers.
  if (isNonIntegerValue || isNonIntegerStepBase || isNonIntegerStep) {
    var valueDecimals = isNonIntegerValue ? countDecimals(value) : 0;
    var stepBaseDecimals = isNonIntegerStepBase ? countDecimals(stepBase) : 0;
    var stepDecimals = isNonIntegerStep ? countDecimals(step) : 0;

    var decimalCount = Math.max(valueDecimals, stepBaseDecimals, stepDecimals);
    var multiplier = Math.pow(10, decimalCount);

    value = value * multiplier;
    stepBase = stepBase * multiplier;
    step = step * multiplier;

    if (isNonIntegerValue) value = Math.round(value);
    if (isNonIntegerStepBase) stepBase = Math.round(stepBase);
    if (isNonIntegerStep) step = Math.round(step);
  }

  return (value - stepBase) % step === 0;
}

function numberInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  badInputChecker(scope, element, attr, ctrl);
  numberFormatterParser(ctrl);
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);

  var minVal;
  var maxVal;

  if (isDefined(attr.min) || attr.ngMin) {
    ctrl.$validators.min = function(value) {
      return ctrl.$isEmpty(value) || isUndefined(minVal) || value >= minVal;
    };

    attr.$observe('min', function(val) {
      minVal = parseNumberAttrVal(val);
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    });
  }

  if (isDefined(attr.max) || attr.ngMax) {
    ctrl.$validators.max = function(value) {
      return ctrl.$isEmpty(value) || isUndefined(maxVal) || value <= maxVal;
    };

    attr.$observe('max', function(val) {
      maxVal = parseNumberAttrVal(val);
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    });
  }

  if (isDefined(attr.step) || attr.ngStep) {
    var stepVal;
    ctrl.$validators.step = function(modelValue, viewValue) {
      return ctrl.$isEmpty(viewValue) || isUndefined(stepVal) ||
             isValidForStep(viewValue, minVal || 0, stepVal);
    };

    attr.$observe('step', function(val) {
      stepVal = parseNumberAttrVal(val);
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    });
  }
}

function rangeInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  badInputChecker(scope, element, attr, ctrl);
  numberFormatterParser(ctrl);
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);

  var supportsRange = ctrl.$$hasNativeValidators && element[0].type === 'range',
      minVal = supportsRange ? 0 : undefined,
      maxVal = supportsRange ? 100 : undefined,
      stepVal = supportsRange ? 1 : undefined,
      validity = element[0].validity,
      hasMinAttr = isDefined(attr.min),
      hasMaxAttr = isDefined(attr.max),
      hasStepAttr = isDefined(attr.step);

  var originalRender = ctrl.$render;

  ctrl.$render = supportsRange && isDefined(validity.rangeUnderflow) && isDefined(validity.rangeOverflow) ?
    //Browsers that implement range will set these values automatically, but reading the adjusted values after
    //$render would cause the min / max validators to be applied with the wrong value
    function rangeRender() {
      originalRender();
      ctrl.$setViewValue(element.val());
    } :
    originalRender;

  if (hasMinAttr) {
    ctrl.$validators.min = supportsRange ?
      // Since all browsers set the input to a valid value, we don't need to check validity
      function noopMinValidator() { return true; } :
      // non-support browsers validate the min val
      function minValidator(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || isUndefined(minVal) || viewValue >= minVal;
      };

    setInitialValueAndObserver('min', minChange);
  }

  if (hasMaxAttr) {
    ctrl.$validators.max = supportsRange ?
      // Since all browsers set the input to a valid value, we don't need to check validity
      function noopMaxValidator() { return true; } :
      // non-support browsers validate the max val
      function maxValidator(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || isUndefined(maxVal) || viewValue <= maxVal;
      };

    setInitialValueAndObserver('max', maxChange);
  }

  if (hasStepAttr) {
    ctrl.$validators.step = supportsRange ?
      function nativeStepValidator() {
        // Currently, only FF implements the spec on step change correctly (i.e. adjusting the
        // input element value to a valid value). It's possible that other browsers set the stepMismatch
        // validity error instead, so we can at least report an error in that case.
        return !validity.stepMismatch;
      } :
      // ngStep doesn't set the setp attr, so the browser doesn't adjust the input value as setting step would
      function stepValidator(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || isUndefined(stepVal) ||
               isValidForStep(viewValue, minVal || 0, stepVal);
      };

    setInitialValueAndObserver('step', stepChange);
  }

  function setInitialValueAndObserver(htmlAttrName, changeFn) {
    // interpolated attributes set the attribute value only after a digest, but we need the
    // attribute value when the input is first rendered, so that the browser can adjust the
    // input value based on the min/max value
    element.attr(htmlAttrName, attr[htmlAttrName]);
    attr.$observe(htmlAttrName, changeFn);
  }

  function minChange(val) {
    minVal = parseNumberAttrVal(val);
    // ignore changes before model is initialized
    if (isNumberNaN(ctrl.$modelValue)) {
      return;
    }

    if (supportsRange) {
      var elVal = element.val();
      // IE11 doesn't set the el val correctly if the minVal is greater than the element value
      if (minVal > elVal) {
        elVal = minVal;
        element.val(elVal);
      }
      ctrl.$setViewValue(elVal);
    } else {
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    }
  }

  function maxChange(val) {
    maxVal = parseNumberAttrVal(val);
    // ignore changes before model is initialized
    if (isNumberNaN(ctrl.$modelValue)) {
      return;
    }

    if (supportsRange) {
      var elVal = element.val();
      // IE11 doesn't set the el val correctly if the maxVal is less than the element value
      if (maxVal < elVal) {
        element.val(maxVal);
        // IE11 and Chrome don't set the value to the minVal when max < min
        elVal = maxVal < minVal ? minVal : maxVal;
      }
      ctrl.$setViewValue(elVal);
    } else {
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    }
  }

  function stepChange(val) {
    stepVal = parseNumberAttrVal(val);
    // ignore changes before model is initialized
    if (isNumberNaN(ctrl.$modelValue)) {
      return;
    }

    // Some browsers don't adjust the input value correctly, but set the stepMismatch error
    if (supportsRange && ctrl.$viewValue !== element.val()) {
      ctrl.$setViewValue(element.val());
    } else {
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    }
  }
}

function urlInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  // Note: no badInputChecker here by purpose as `url` is only a validation
  // in browsers, i.e. we can always read out input.value even if it is not valid!
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
  stringBasedInputType(ctrl);

  ctrl.$$parserName = 'url';
  ctrl.$validators.url = function(modelValue, viewValue) {
    var value = modelValue || viewValue;
    return ctrl.$isEmpty(value) || URL_REGEXP.test(value);
  };
}

function emailInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  // Note: no badInputChecker here by purpose as `url` is only a validation
  // in browsers, i.e. we can always read out input.value even if it is not valid!
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
  stringBasedInputType(ctrl);

  ctrl.$$parserName = 'email';
  ctrl.$validators.email = function(modelValue, viewValue) {
    var value = modelValue || viewValue;
    return ctrl.$isEmpty(value) || EMAIL_REGEXP.test(value);
  };
}

function radioInputType(scope, element, attr, ctrl) {
  var doTrim = !attr.ngTrim || trim(attr.ngTrim) !== 'false';
  // make the name unique, if not defined
  if (isUndefined(attr.name)) {
    element.attr('name', nextUid());
  }

  var listener = function(ev) {
    var value;
    if (element[0].checked) {
      value = attr.value;
      if (doTrim) {
        value = trim(value);
      }
      ctrl.$setViewValue(value, ev && ev.type);
    }
  };

  element.on('click', listener);

  ctrl.$render = function() {
    var value = attr.value;
    if (doTrim) {
      value = trim(value);
    }
    element[0].checked = (value === ctrl.$viewValue);
  };

  attr.$observe('value', ctrl.$render);
}

function parseConstantExpr($parse, context, name, expression, fallback) {
  var parseFn;
  if (isDefined(expression)) {
    parseFn = $parse(expression);
    if (!parseFn.constant) {
      throw ngModelMinErr('constexpr', 'Expected constant expression for `{0}`, but saw ' +
                                   '`{1}`.', name, expression);
    }
    return parseFn(context);
  }
  return fallback;
}

function checkboxInputType(scope, element, attr, ctrl, $sniffer, $browser, $filter, $parse) {
  var trueValue = parseConstantExpr($parse, scope, 'ngTrueValue', attr.ngTrueValue, true);
  var falseValue = parseConstantExpr($parse, scope, 'ngFalseValue', attr.ngFalseValue, false);

  var listener = function(ev) {
    ctrl.$setViewValue(element[0].checked, ev && ev.type);
  };

  element.on('click', listener);

  ctrl.$render = function() {
    element[0].checked = ctrl.$viewValue;
  };

  // Override the standard `$isEmpty` because the $viewValue of an empty checkbox is always set to `false`
  // This is because of the parser below, which compares the `$modelValue` with `trueValue` to convert
  // it to a boolean.
  ctrl.$isEmpty = function(value) {
    return value === false;
  };

  ctrl.$formatters.push(function(value) {
    return equals(value, trueValue);
  });

  ctrl.$parsers.push(function(value) {
    return value ? trueValue : falseValue;
  });
}


/**
 * @ngdoc directive
 * @name textarea
 * @restrict E
 *
 * @description
 * HTML textarea element control with angular data-binding. The data-binding and validation
 * properties of this element are exactly the same as those of the
 * {@link ng.directive:input input element}.
 *
 * @param {string} ngModel Assignable angular expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} required Sets `required` validation error key if the value is not entered.
 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
 *    `required` when you want to data-bind to the `required` attribute.
 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
 *    minlength.
 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of any
 *    length.
 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
 *    does not match a RegExp found by evaluating the Angular expression given in the attribute value.
 *    If the expression evaluates to a RegExp object, then this is used directly.
 *    If the expression evaluates to a string, then it will be converted to a RegExp
 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
 *    `new RegExp('^abc$')`.<br />
 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
 *    start at the index of the last search's match, thus not taking the whole input value into
 *    account.
 * @param {string=} ngChange Angular expression to be executed when input changes due to user
 *    interaction with the input element.
 * @param {boolean=} [ngTrim=true] If set to false Angular will not automatically trim the input.
 *
 * @knownIssue
 *
 * When specifying the `placeholder` attribute of `<textarea>`, Internet Explorer will temporarily
 * insert the placeholder value as the textarea's content. If the placeholder value contains
 * interpolation (`{{ ... }}`), an error will be logged in the console when Angular tries to update
 * the value of the by-then-removed text node. This doesn't affect the functionality of the
 * textarea, but can be undesirable.
 *
 * You can work around this Internet Explorer issue by using `ng-attr-placeholder` instead of
 * `placeholder` on textareas, whenever you need interpolation in the placeholder value. You can
 * find more details on `ngAttr` in the
 * [Interpolation](guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes) section of the
 * Developer Guide.
 */


/**
 * @ngdoc directive
 * @name input
 * @restrict E
 *
 * @description
 * HTML input element control. When used together with {@link ngModel `ngModel`}, it provides data-binding,
 * input state control, and validation.
 * Input control follows HTML5 input types and polyfills the HTML5 validation behavior for older browsers.
 *
 * <div class="alert alert-warning">
 * **Note:** Not every feature offered is available for all input types.
 * Specifically, data binding and event handling via `ng-model` is unsupported for `input[file]`.
 * </div>
 *
 * @param {string} ngModel Assignable angular expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} required Sets `required` validation error key if the value is not entered.
 * @param {boolean=} ngRequired Sets `required` attribute if set to true
 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
 *    minlength.
 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of any
 *    length.
 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
 *    value does not match a RegExp found by evaluating the Angular expression given in the attribute value.
 *    If the expression evaluates to a RegExp object, then this is used directly.
 *    If the expression evaluates to a string, then it will be converted to a RegExp
 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
 *    `new RegExp('^abc$')`.<br />
 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
 *    start at the index of the last search's match, thus not taking the whole input value into
 *    account.
 * @param {string=} ngChange Angular expression to be executed when input changes due to user
 *    interaction with the input element.
 * @param {boolean=} [ngTrim=true] If set to false Angular will not automatically trim the input.
 *    This parameter is ignored for input[type=password] controls, which will never trim the
 *    input.
 *
 * @example
    <example name="input-directive" module="inputExample">
      <file name="index.html">
       <script>
          angular.module('inputExample', [])
            .controller('ExampleController', ['$scope', function($scope) {
              $scope.user = {name: 'guest', last: 'visitor'};
            }]);
       </script>
       <div ng-controller="ExampleController">
         <form name="myForm">
           <label>
              User name:
              <input type="text" name="userName" ng-model="user.name" required>
           </label>
           <div role="alert">
             <span class="error" ng-show="myForm.userName.$error.required">
              Required!</span>
           </div>
           <label>
              Last name:
              <input type="text" name="lastName" ng-model="user.last"
              ng-minlength="3" ng-maxlength="10">
           </label>
           <div role="alert">
             <span class="error" ng-show="myForm.lastName.$error.minlength">
               Too short!</span>
             <span class="error" ng-show="myForm.lastName.$error.maxlength">
               Too long!</span>
           </div>
         </form>
         <hr>
         <tt>user = {{user}}</tt><br/>
         <tt>myForm.userName.$valid = {{myForm.userName.$valid}}</tt><br/>
         <tt>myForm.userName.$error = {{myForm.userName.$error}}</tt><br/>
         <tt>myForm.lastName.$valid = {{myForm.lastName.$valid}}</tt><br/>
         <tt>myForm.lastName.$error = {{myForm.lastName.$error}}</tt><br/>
         <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
         <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
         <tt>myForm.$error.minlength = {{!!myForm.$error.minlength}}</tt><br/>
         <tt>myForm.$error.maxlength = {{!!myForm.$error.maxlength}}</tt><br/>
       </div>
      </file>
      <file name="protractor.js" type="protractor">
        var user = element(by.exactBinding('user'));
        var userNameValid = element(by.binding('myForm.userName.$valid'));
        var lastNameValid = element(by.binding('myForm.lastName.$valid'));
        var lastNameError = element(by.binding('myForm.lastName.$error'));
        var formValid = element(by.binding('myForm.$valid'));
        var userNameInput = element(by.model('user.name'));
        var userLastInput = element(by.model('user.last'));

        it('should initialize to model', function() {
          expect(user.getText()).toContain('{"name":"guest","last":"visitor"}');
          expect(userNameValid.getText()).toContain('true');
          expect(formValid.getText()).toContain('true');
        });

        it('should be invalid if empty when required', function() {
          userNameInput.clear();
          userNameInput.sendKeys('');

          expect(user.getText()).toContain('{"last":"visitor"}');
          expect(userNameValid.getText()).toContain('false');
          expect(formValid.getText()).toContain('false');
        });

        it('should be valid if empty when min length is set', function() {
          userLastInput.clear();
          userLastInput.sendKeys('');

          expect(user.getText()).toContain('{"name":"guest","last":""}');
          expect(lastNameValid.getText()).toContain('true');
          expect(formValid.getText()).toContain('true');
        });

        it('should be invalid if less than required min length', function() {
          userLastInput.clear();
          userLastInput.sendKeys('xx');

          expect(user.getText()).toContain('{"name":"guest"}');
          expect(lastNameValid.getText()).toContain('false');
          expect(lastNameError.getText()).toContain('minlength');
          expect(formValid.getText()).toContain('false');
        });

        it('should be invalid if longer than max length', function() {
          userLastInput.clear();
          userLastInput.sendKeys('some ridiculously long name');

          expect(user.getText()).toContain('{"name":"guest"}');
          expect(lastNameValid.getText()).toContain('false');
          expect(lastNameError.getText()).toContain('maxlength');
          expect(formValid.getText()).toContain('false');
        });
      </file>
    </example>
 */
var inputDirective = ['$browser', '$sniffer', '$filter', '$parse',
    function($browser, $sniffer, $filter, $parse) {
  return {
    restrict: 'E',
    require: ['?ngModel'],
    link: {
      pre: function(scope, element, attr, ctrls) {
        if (ctrls[0]) {
          (inputType[lowercase(attr.type)] || inputType.text)(scope, element, attr, ctrls[0], $sniffer,
                                                              $browser, $filter, $parse);
        }
      }
    }
  };
}];



var CONSTANT_VALUE_REGEXP = /^(true|false|\d+)$/;
/**
 * @ngdoc directive
 * @name ngValue
 *
 * @description
 * Binds the given expression to the value of the element.
 *
 * It is mainly used on {@link input[radio] `input[radio]`} and option elements,
 * so that when the element is selected, the {@link ngModel `ngModel`} of that element (or its
 * {@link select `select`} parent element) is set to the bound value. It is especially useful
 * for dynamically generated lists using {@link ngRepeat `ngRepeat`}, as shown below.
 *
 * It can also be used to achieve one-way binding of a given expression to an input element
 * such as an `input[text]` or a `textarea`, when that element does not use ngModel.
 *
 * @element input
 * @param {string=} ngValue angular expression, whose value will be bound to the `value` attribute
 * and `value` property of the element.
 *
 * @example
    <example name="ngValue-directive" module="valueExample">
      <file name="index.html">
       <script>
          angular.module('valueExample', [])
            .controller('ExampleController', ['$scope', function($scope) {
              $scope.names = ['pizza', 'unicorns', 'robots'];
              $scope.my = { favorite: 'unicorns' };
            }]);
       </script>
        <form ng-controller="ExampleController">
          <h2>Which is your favorite?</h2>
            <label ng-repeat="name in names" for="{{name}}">
              {{name}}
              <input type="radio"
                     ng-model="my.favorite"
                     ng-value="name"
                     id="{{name}}"
                     name="favorite">
            </label>
          <div>You chose {{my.favorite}}</div>
        </form>
      </file>
      <file name="protractor.js" type="protractor">
        var favorite = element(by.binding('my.favorite'));

        it('should initialize to model', function() {
          expect(favorite.getText()).toContain('unicorns');
        });
        it('should bind the values to the inputs', function() {
          element.all(by.model('my.favorite')).get(0).click();
          expect(favorite.getText()).toContain('pizza');
        });
      </file>
    </example>
 */
var ngValueDirective = function() {
  /**
   *  inputs use the value attribute as their default value if the value property is not set.
   *  Once the value property has been set (by adding input), it will not react to changes to
   *  the value attribute anymore. Setting both attribute and property fixes this behavior, and
   *  makes it possible to use ngValue as a sort of one-way bind.
   */
  function updateElementValue(element, attr, value) {
    // Support: IE9 only
    // In IE9 values are converted to string (e.g. `input.value = null` results in `input.value === 'null'`).
    var propValue = isDefined(value) ? value : (msie === 9) ? '' : null;
    element.prop('value', propValue);
    attr.$set('value', value);
  }

  return {
    restrict: 'A',
    priority: 100,
    compile: function(tpl, tplAttr) {
      if (CONSTANT_VALUE_REGEXP.test(tplAttr.ngValue)) {
        return function ngValueConstantLink(scope, elm, attr) {
          var value = scope.$eval(attr.ngValue);
          updateElementValue(elm, attr, value);
        };
      } else {
        return function ngValueLink(scope, elm, attr) {
          scope.$watch(attr.ngValue, function valueWatchAction(value) {
            updateElementValue(elm, attr, value);
          });
        };
      }
    }
  };
};

/**
 * @ngdoc directive
 * @name ngBind
 * @restrict AC
 *
 * @description
 * The `ngBind` attribute tells Angular to replace the text content of the specified HTML element
 * with the value of a given expression, and to update the text content when the value of that
 * expression changes.
 *
 * Typically, you don't use `ngBind` directly, but instead you use the double curly markup like
 * `{{ expression }}` which is similar but less verbose.
 *
 * It is preferable to use `ngBind` instead of `{{ expression }}` if a template is momentarily
 * displayed by the browser in its raw state before Angular compiles it. Since `ngBind` is an
 * element attribute, it makes the bindings invisible to the user while the page is loading.
 *
 * An alternative solution to this problem would be using the
 * {@link ng.directive:ngCloak ngCloak} directive.
 *
 *
 * @element ANY
 * @param {expression} ngBind {@link guide/expression Expression} to evaluate.
 *
 * @example
 * Enter a name in the Live Preview text box; the greeting below the text box changes instantly.
   <example module="bindExample" name="ng-bind">
     <file name="index.html">
       <script>
         angular.module('bindExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.name = 'Whirled';
           }]);
       </script>
       <div ng-controller="ExampleController">
         <label>Enter name: <input type="text" ng-model="name"></label><br>
         Hello <span ng-bind="name"></span>!
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-bind', function() {
         var nameInput = element(by.model('name'));

         expect(element(by.binding('name')).getText()).toBe('Whirled');
         nameInput.clear();
         nameInput.sendKeys('world');
         expect(element(by.binding('name')).getText()).toBe('world');
       });
     </file>
   </example>
 */
var ngBindDirective = ['$compile', function($compile) {
  return {
    restrict: 'AC',
    compile: function ngBindCompile(templateElement) {
      $compile.$$addBindingClass(templateElement);
      return function ngBindLink(scope, element, attr) {
        $compile.$$addBindingInfo(element, attr.ngBind);
        element = element[0];
        scope.$watch(attr.ngBind, function ngBindWatchAction(value) {
          element.textContent = stringify(value);
        });
      };
    }
  };
}];


/**
 * @ngdoc directive
 * @name ngBindTemplate
 *
 * @description
 * The `ngBindTemplate` directive specifies that the element
 * text content should be replaced with the interpolation of the template
 * in the `ngBindTemplate` attribute.
 * Unlike `ngBind`, the `ngBindTemplate` can contain multiple `{{` `}}`
 * expressions. This directive is needed since some HTML elements
 * (such as TITLE and OPTION) cannot contain SPAN elements.
 *
 * @element ANY
 * @param {string} ngBindTemplate template of form
 *   <tt>{{</tt> <tt>expression</tt> <tt>}}</tt> to eval.
 *
 * @example
 * Try it here: enter text in text box and watch the greeting change.
   <example module="bindExample" name="ng-bind-template">
     <file name="index.html">
       <script>
         angular.module('bindExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.salutation = 'Hello';
             $scope.name = 'World';
           }]);
       </script>
       <div ng-controller="ExampleController">
        <label>Salutation: <input type="text" ng-model="salutation"></label><br>
        <label>Name: <input type="text" ng-model="name"></label><br>
        <pre ng-bind-template="{{salutation}} {{name}}!"></pre>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-bind', function() {
         var salutationElem = element(by.binding('salutation'));
         var salutationInput = element(by.model('salutation'));
         var nameInput = element(by.model('name'));

         expect(salutationElem.getText()).toBe('Hello World!');

         salutationInput.clear();
         salutationInput.sendKeys('Greetings');
         nameInput.clear();
         nameInput.sendKeys('user');

         expect(salutationElem.getText()).toBe('Greetings user!');
       });
     </file>
   </example>
 */
var ngBindTemplateDirective = ['$interpolate', '$compile', function($interpolate, $compile) {
  return {
    compile: function ngBindTemplateCompile(templateElement) {
      $compile.$$addBindingClass(templateElement);
      return function ngBindTemplateLink(scope, element, attr) {
        var interpolateFn = $interpolate(element.attr(attr.$attr.ngBindTemplate));
        $compile.$$addBindingInfo(element, interpolateFn.expressions);
        element = element[0];
        attr.$observe('ngBindTemplate', function(value) {
          element.textContent = isUndefined(value) ? '' : value;
        });
      };
    }
  };
}];


/**
 * @ngdoc directive
 * @name ngBindHtml
 *
 * @description
 * Evaluates the expression and inserts the resulting HTML into the element in a secure way. By default,
 * the resulting HTML content will be sanitized using the {@link ngSanitize.$sanitize $sanitize} service.
 * To utilize this functionality, ensure that `$sanitize` is available, for example, by including {@link
 * ngSanitize} in your module's dependencies (not in core Angular). In order to use {@link ngSanitize}
 * in your module's dependencies, you need to include "angular-sanitize.js" in your application.
 *
 * You may also bypass sanitization for values you know are safe. To do so, bind to
 * an explicitly trusted value via {@link ng.$sce#trustAsHtml $sce.trustAsHtml}.  See the example
 * under {@link ng.$sce#show-me-an-example-using-sce- Strict Contextual Escaping (SCE)}.
 *
 * Note: If a `$sanitize` service is unavailable and the bound value isn't explicitly trusted, you
 * will have an exception (instead of an exploit.)
 *
 * @element ANY
 * @param {expression} ngBindHtml {@link guide/expression Expression} to evaluate.
 *
 * @example

   <example module="bindHtmlExample" deps="angular-sanitize.js" name="ng-bind-html">
     <file name="index.html">
       <div ng-controller="ExampleController">
        <p ng-bind-html="myHTML"></p>
       </div>
     </file>

     <file name="script.js">
       angular.module('bindHtmlExample', ['ngSanitize'])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.myHTML =
              'I am an <code>HTML</code>string with ' +
              '<a href="#">links!</a> and other <em>stuff</em>';
         }]);
     </file>

     <file name="protractor.js" type="protractor">
       it('should check ng-bind-html', function() {
         expect(element(by.binding('myHTML')).getText()).toBe(
             'I am an HTMLstring with links! and other stuff');
       });
     </file>
   </example>
 */
var ngBindHtmlDirective = ['$sce', '$parse', '$compile', function($sce, $parse, $compile) {
  return {
    restrict: 'A',
    compile: function ngBindHtmlCompile(tElement, tAttrs) {
      var ngBindHtmlGetter = $parse(tAttrs.ngBindHtml);
      var ngBindHtmlWatch = $parse(tAttrs.ngBindHtml, function sceValueOf(val) {
        // Unwrap the value to compare the actual inner safe value, not the wrapper object.
        return $sce.valueOf(val);
      });
      $compile.$$addBindingClass(tElement);

      return function ngBindHtmlLink(scope, element, attr) {
        $compile.$$addBindingInfo(element, attr.ngBindHtml);

        scope.$watch(ngBindHtmlWatch, function ngBindHtmlWatchAction() {
          // The watched value is the unwrapped value. To avoid re-escaping, use the direct getter.
          var value = ngBindHtmlGetter(scope);
          element.html($sce.getTrustedHtml(value) || '');
        });
      };
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngChange
 *
 * @description
 * Evaluate the given expression when the user changes the input.
 * The expression is evaluated immediately, unlike the JavaScript onchange event
 * which only triggers at the end of a change (usually, when the user leaves the
 * form element or presses the return key).
 *
 * The `ngChange` expression is only evaluated when a change in the input value causes
 * a new value to be committed to the model.
 *
 * It will not be evaluated:
 * * if the value returned from the `$parsers` transformation pipeline has not changed
 * * if the input has continued to be invalid since the model will stay `null`
 * * if the model is changed programmatically and not by a change to the input value
 *
 *
 * Note, this directive requires `ngModel` to be present.
 *
 * @element input
 * @param {expression} ngChange {@link guide/expression Expression} to evaluate upon change
 * in input value.
 *
 * @example
 * <example name="ngChange-directive" module="changeExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('changeExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.counter = 0;
 *           $scope.change = function() {
 *             $scope.counter++;
 *           };
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <input type="checkbox" ng-model="confirmed" ng-change="change()" id="ng-change-example1" />
 *       <input type="checkbox" ng-model="confirmed" id="ng-change-example2" />
 *       <label for="ng-change-example2">Confirmed</label><br />
 *       <tt>debug = {{confirmed}}</tt><br/>
 *       <tt>counter = {{counter}}</tt><br/>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     var counter = element(by.binding('counter'));
 *     var debug = element(by.binding('confirmed'));
 *
 *     it('should evaluate the expression if changing from view', function() {
 *       expect(counter.getText()).toContain('0');
 *
 *       element(by.id('ng-change-example1')).click();
 *
 *       expect(counter.getText()).toContain('1');
 *       expect(debug.getText()).toContain('true');
 *     });
 *
 *     it('should not evaluate the expression if changing from model', function() {
 *       element(by.id('ng-change-example2')).click();

 *       expect(counter.getText()).toContain('0');
 *       expect(debug.getText()).toContain('true');
 *     });
 *   </file>
 * </example>
 */
var ngChangeDirective = valueFn({
  restrict: 'A',
  require: 'ngModel',
  link: function(scope, element, attr, ctrl) {
    ctrl.$viewChangeListeners.push(function() {
      scope.$eval(attr.ngChange);
    });
  }
});

/* exported
  ngClassDirective,
  ngClassEvenDirective,
  ngClassOddDirective
*/

function classDirective(name, selector) {
  name = 'ngClass' + name;
  var indexWatchExpression;

  return ['$parse', function($parse) {
    return {
      restrict: 'AC',
      link: function(scope, element, attr) {
        var expression = attr[name].trim();
        var isOneTime = (expression.charAt(0) === ':') && (expression.charAt(1) === ':');

        var watchInterceptor = isOneTime ? toFlatValue : toClassString;
        var watchExpression = $parse(expression, watchInterceptor);
        var watchAction = isOneTime ? ngClassOneTimeWatchAction : ngClassWatchAction;

        var classCounts = element.data('$classCounts');
        var oldModulo = true;
        var oldClassString;

        if (!classCounts) {
          // Use createMap() to prevent class assumptions involving property
          // names in Object.prototype
          classCounts = createMap();
          element.data('$classCounts', classCounts);
        }

        if (name !== 'ngClass') {
          if (!indexWatchExpression) {
            indexWatchExpression = $parse('$index', function moduloTwo($index) {
              // eslint-disable-next-line no-bitwise
              return $index & 1;
            });
          }

          scope.$watch(indexWatchExpression, ngClassIndexWatchAction);
        }

        scope.$watch(watchExpression, watchAction, isOneTime);

        function addClasses(classString) {
          classString = digestClassCounts(split(classString), 1);
          attr.$addClass(classString);
        }

        function removeClasses(classString) {
          classString = digestClassCounts(split(classString), -1);
          attr.$removeClass(classString);
        }

        function updateClasses(oldClassString, newClassString) {
          var oldClassArray = split(oldClassString);
          var newClassArray = split(newClassString);

          var toRemoveArray = arrayDifference(oldClassArray, newClassArray);
          var toAddArray = arrayDifference(newClassArray, oldClassArray);

          var toRemoveString = digestClassCounts(toRemoveArray, -1);
          var toAddString = digestClassCounts(toAddArray, 1);

          attr.$addClass(toAddString);
          attr.$removeClass(toRemoveString);
        }

        function digestClassCounts(classArray, count) {
          var classesToUpdate = [];

          forEach(classArray, function(className) {
            if (count > 0 || classCounts[className]) {
              classCounts[className] = (classCounts[className] || 0) + count;
              if (classCounts[className] === +(count > 0)) {
                classesToUpdate.push(className);
              }
            }
          });

          return classesToUpdate.join(' ');
        }

        function ngClassIndexWatchAction(newModulo) {
          // This watch-action should run before the `ngClass[OneTime]WatchAction()`, thus it
          // adds/removes `oldClassString`. If the `ngClass` expression has changed as well, the
          // `ngClass[OneTime]WatchAction()` will update the classes.
          if (newModulo === selector) {
            addClasses(oldClassString);
          } else {
            removeClasses(oldClassString);
          }

          oldModulo = newModulo;
        }

        function ngClassOneTimeWatchAction(newClassValue) {
          var newClassString = toClassString(newClassValue);

          if (newClassString !== oldClassString) {
            ngClassWatchAction(newClassString);
          }
        }

        function ngClassWatchAction(newClassString) {
          if (oldModulo === selector) {
            updateClasses(oldClassString, newClassString);
          }

          oldClassString = newClassString;
        }
      }
    };
  }];

  // Helpers
  function arrayDifference(tokens1, tokens2) {
    if (!tokens1 || !tokens1.length) return [];
    if (!tokens2 || !tokens2.length) return tokens1;

    var values = [];

    outer:
    for (var i = 0; i < tokens1.length; i++) {
      var token = tokens1[i];
      for (var j = 0; j < tokens2.length; j++) {
        if (token === tokens2[j]) continue outer;
      }
      values.push(token);
    }

    return values;
  }

  function split(classString) {
    return classString && classString.split(' ');
  }

  function toClassString(classValue) {
    var classString = classValue;

    if (isArray(classValue)) {
      classString = classValue.map(toClassString).join(' ');
    } else if (isObject(classValue)) {
      classString = Object.keys(classValue).
        filter(function(key) { return classValue[key]; }).
        join(' ');
    }

    return classString;
  }

  function toFlatValue(classValue) {
    var flatValue = classValue;

    if (isArray(classValue)) {
      flatValue = classValue.map(toFlatValue);
    } else if (isObject(classValue)) {
      var hasUndefined = false;

      flatValue = Object.keys(classValue).filter(function(key) {
        var value = classValue[key];

        if (!hasUndefined && isUndefined(value)) {
          hasUndefined = true;
        }

        return value;
      });

      if (hasUndefined) {
        // Prevent the `oneTimeLiteralWatchInterceptor` from unregistering
        // the watcher, by including at least one `undefined` value.
        flatValue.push(undefined);
      }
    }

    return flatValue;
  }
}

/**
 * @ngdoc directive
 * @name ngClass
 * @restrict AC
 *
 * @description
 * The `ngClass` directive allows you to dynamically set CSS classes on an HTML element by databinding
 * an expression that represents all classes to be added.
 *
 * The directive operates in three different ways, depending on which of three types the expression
 * evaluates to:
 *
 * 1. If the expression evaluates to a string, the string should be one or more space-delimited class
 * names.
 *
 * 2. If the expression evaluates to an object, then for each key-value pair of the
 * object with a truthy value the corresponding key is used as a class name.
 *
 * 3. If the expression evaluates to an array, each element of the array should either be a string as in
 * type 1 or an object as in type 2. This means that you can mix strings and objects together in an array
 * to give you more control over what CSS classes appear. See the code below for an example of this.
 *
 *
 * The directive won't add duplicate classes if a particular class was already set.
 *
 * When the expression changes, the previously added classes are removed and only then are the
 * new classes added.
 *
 * @knownIssue
 * You should not use {@link guide/interpolation interpolation} in the value of the `class`
 * attribute, when using the `ngClass` directive on the same element.
 * See {@link guide/interpolation#known-issues here} for more info.
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#addClass addClass}       | just before the class is applied to the element   |
 * | {@link ng.$animate#removeClass removeClass} | just before the class is removed from the element |
 *
 * @element ANY
 * @param {expression} ngClass {@link guide/expression Expression} to eval. The result
 *   of the evaluation can be a string representing space delimited class
 *   names, an array, or a map of class names to boolean values. In the case of a map, the
 *   names of the properties whose values are truthy will be added as css classes to the
 *   element.
 *
 * @example Example that demonstrates basic bindings via ngClass directive.
   <example name="ng-class">
     <file name="index.html">
       <p ng-class="{strike: deleted, bold: important, 'has-error': error}">Map Syntax Example</p>
       <label>
          <input type="checkbox" ng-model="deleted">
          deleted (apply "strike" class)
       </label><br>
       <label>
          <input type="checkbox" ng-model="important">
          important (apply "bold" class)
       </label><br>
       <label>
          <input type="checkbox" ng-model="error">
          error (apply "has-error" class)
       </label>
       <hr>
       <p ng-class="style">Using String Syntax</p>
       <input type="text" ng-model="style"
              placeholder="Type: bold strike red" aria-label="Type: bold strike red">
       <hr>
       <p ng-class="[style1, style2, style3]">Using Array Syntax</p>
       <input ng-model="style1"
              placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red"><br>
       <input ng-model="style2"
              placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red 2"><br>
       <input ng-model="style3"
              placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red 3"><br>
       <hr>
       <p ng-class="[style4, {orange: warning}]">Using Array and Map Syntax</p>
       <input ng-model="style4" placeholder="Type: bold, strike" aria-label="Type: bold, strike"><br>
       <label><input type="checkbox" ng-model="warning"> warning (apply "orange" class)</label>
     </file>
     <file name="style.css">
       .strike {
           text-decoration: line-through;
       }
       .bold {
           font-weight: bold;
       }
       .red {
           color: red;
       }
       .has-error {
           color: red;
           background-color: yellow;
       }
       .orange {
           color: orange;
       }
     </file>
     <file name="protractor.js" type="protractor">
       var ps = element.all(by.css('p'));

       it('should let you toggle the class', function() {

         expect(ps.first().getAttribute('class')).not.toMatch(/bold/);
         expect(ps.first().getAttribute('class')).not.toMatch(/has-error/);

         element(by.model('important')).click();
         expect(ps.first().getAttribute('class')).toMatch(/bold/);

         element(by.model('error')).click();
         expect(ps.first().getAttribute('class')).toMatch(/has-error/);
       });

       it('should let you toggle string example', function() {
         expect(ps.get(1).getAttribute('class')).toBe('');
         element(by.model('style')).clear();
         element(by.model('style')).sendKeys('red');
         expect(ps.get(1).getAttribute('class')).toBe('red');
       });

       it('array example should have 3 classes', function() {
         expect(ps.get(2).getAttribute('class')).toBe('');
         element(by.model('style1')).sendKeys('bold');
         element(by.model('style2')).sendKeys('strike');
         element(by.model('style3')).sendKeys('red');
         expect(ps.get(2).getAttribute('class')).toBe('bold strike red');
       });

       it('array with map example should have 2 classes', function() {
         expect(ps.last().getAttribute('class')).toBe('');
         element(by.model('style4')).sendKeys('bold');
         element(by.model('warning')).click();
         expect(ps.last().getAttribute('class')).toBe('bold orange');
       });
     </file>
   </example>

   ## Animations

   The example below demonstrates how to perform animations using ngClass.

   <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-class">
     <file name="index.html">
      <input id="setbtn" type="button" value="set" ng-click="myVar='my-class'">
      <input id="clearbtn" type="button" value="clear" ng-click="myVar=''">
      <br>
      <span class="base-class" ng-class="myVar">Sample Text</span>
     </file>
     <file name="style.css">
       .base-class {
         transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;
       }

       .base-class.my-class {
         color: red;
         font-size:3em;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-class', function() {
         expect(element(by.css('.base-class')).getAttribute('class')).not.
           toMatch(/my-class/);

         element(by.id('setbtn')).click();

         expect(element(by.css('.base-class')).getAttribute('class')).
           toMatch(/my-class/);

         element(by.id('clearbtn')).click();

         expect(element(by.css('.base-class')).getAttribute('class')).not.
           toMatch(/my-class/);
       });
     </file>
   </example>


   ## ngClass and pre-existing CSS3 Transitions/Animations
   The ngClass directive still supports CSS3 Transitions/Animations even if they do not follow the ngAnimate CSS naming structure.
   Upon animation ngAnimate will apply supplementary CSS classes to track the start and end of an animation, but this will not hinder
   any pre-existing CSS transitions already on the element. To get an idea of what happens during a class-based animation, be sure
   to view the step by step details of {@link $animate#addClass $animate.addClass} and
   {@link $animate#removeClass $animate.removeClass}.
 */
var ngClassDirective = classDirective('', true);

/**
 * @ngdoc directive
 * @name ngClassOdd
 * @restrict AC
 *
 * @description
 * The `ngClassOdd` and `ngClassEven` directives work exactly as
 * {@link ng.directive:ngClass ngClass}, except they work in
 * conjunction with `ngRepeat` and take effect only on odd (even) rows.
 *
 * This directive can be applied only within the scope of an
 * {@link ng.directive:ngRepeat ngRepeat}.
 *
 * @element ANY
 * @param {expression} ngClassOdd {@link guide/expression Expression} to eval. The result
 *   of the evaluation can be a string representing space delimited class names or an array.
 *
 * @example
   <example name="ng-class-odd">
     <file name="index.html">
        <ol ng-init="names=['John', 'Mary', 'Cate', 'Suz']">
          <li ng-repeat="name in names">
           <span ng-class-odd="'odd'" ng-class-even="'even'">
             {{name}}
           </span>
          </li>
        </ol>
     </file>
     <file name="style.css">
       .odd {
         color: red;
       }
       .even {
         color: blue;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-class-odd and ng-class-even', function() {
         expect(element(by.repeater('name in names').row(0).column('name')).getAttribute('class')).
           toMatch(/odd/);
         expect(element(by.repeater('name in names').row(1).column('name')).getAttribute('class')).
           toMatch(/even/);
       });
     </file>
   </example>
 */
var ngClassOddDirective = classDirective('Odd', 0);

/**
 * @ngdoc directive
 * @name ngClassEven
 * @restrict AC
 *
 * @description
 * The `ngClassOdd` and `ngClassEven` directives work exactly as
 * {@link ng.directive:ngClass ngClass}, except they work in
 * conjunction with `ngRepeat` and take effect only on odd (even) rows.
 *
 * This directive can be applied only within the scope of an
 * {@link ng.directive:ngRepeat ngRepeat}.
 *
 * @element ANY
 * @param {expression} ngClassEven {@link guide/expression Expression} to eval. The
 *   result of the evaluation can be a string representing space delimited class names or an array.
 *
 * @example
   <example name="ng-class-even">
     <file name="index.html">
        <ol ng-init="names=['John', 'Mary', 'Cate', 'Suz']">
          <li ng-repeat="name in names">
           <span ng-class-odd="'odd'" ng-class-even="'even'">
             {{name}} &nbsp; &nbsp; &nbsp;
           </span>
          </li>
        </ol>
     </file>
     <file name="style.css">
       .odd {
         color: red;
       }
       .even {
         color: blue;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-class-odd and ng-class-even', function() {
         expect(element(by.repeater('name in names').row(0).column('name')).getAttribute('class')).
           toMatch(/odd/);
         expect(element(by.repeater('name in names').row(1).column('name')).getAttribute('class')).
           toMatch(/even/);
       });
     </file>
   </example>
 */
var ngClassEvenDirective = classDirective('Even', 1);

/**
 * @ngdoc directive
 * @name ngCloak
 * @restrict AC
 *
 * @description
 * The `ngCloak` directive is used to prevent the Angular html template from being briefly
 * displayed by the browser in its raw (uncompiled) form while your application is loading. Use this
 * directive to avoid the undesirable flicker effect caused by the html template display.
 *
 * The directive can be applied to the `<body>` element, but the preferred usage is to apply
 * multiple `ngCloak` directives to small portions of the page to permit progressive rendering
 * of the browser view.
 *
 * `ngCloak` works in cooperation with the following css rule embedded within `angular.js` and
 * `angular.min.js`.
 * For CSP mode please add `angular-csp.css` to your html file (see {@link ng.directive:ngCsp ngCsp}).
 *
 * ```css
 * [ng\:cloak], [ng-cloak], [data-ng-cloak], [x-ng-cloak], .ng-cloak, .x-ng-cloak {
 *   display: none !important;
 * }
 * ```
 *
 * When this css rule is loaded by the browser, all html elements (including their children) that
 * are tagged with the `ngCloak` directive are hidden. When Angular encounters this directive
 * during the compilation of the template it deletes the `ngCloak` element attribute, making
 * the compiled element visible.
 *
 * For the best result, the `angular.js` script must be loaded in the head section of the html
 * document; alternatively, the css rule above must be included in the external stylesheet of the
 * application.
 *
 * @element ANY
 *
 * @example
   <example name="ng-cloak">
     <file name="index.html">
        <div id="template1" ng-cloak>{{ 'hello' }}</div>
        <div id="template2" class="ng-cloak">{{ 'world' }}</div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should remove the template directive and css class', function() {
         expect($('#template1').getAttribute('ng-cloak')).
           toBeNull();
         expect($('#template2').getAttribute('ng-cloak')).
           toBeNull();
       });
     </file>
   </example>
 *
 */
var ngCloakDirective = ngDirective({
  compile: function(element, attr) {
    attr.$set('ngCloak', undefined);
    element.removeClass('ng-cloak');
  }
});

/**
 * @ngdoc directive
 * @name ngController
 *
 * @description
 * The `ngController` directive attaches a controller class to the view. This is a key aspect of how angular
 * supports the principles behind the Model-View-Controller design pattern.
 *
 * MVC components in angular:
 *
 * * Model — Models are the properties of a scope; scopes are attached to the DOM where scope properties
 *   are accessed through bindings.
 * * View — The template (HTML with data bindings) that is rendered into the View.
 * * Controller — The `ngController` directive specifies a Controller class; the class contains business
 *   logic behind the application to decorate the scope with functions and values
 *
 * Note that you can also attach controllers to the DOM by declaring it in a route definition
 * via the {@link ngRoute.$route $route} service. A common mistake is to declare the controller
 * again using `ng-controller` in the template itself.  This will cause the controller to be attached
 * and executed twice.
 *
 * @element ANY
 * @scope
 * @priority 500
 * @param {expression} ngController Name of a constructor function registered with the current
 * {@link ng.$controllerProvider $controllerProvider} or an {@link guide/expression expression}
 * that on the current scope evaluates to a constructor function.
 *
 * The controller instance can be published into a scope property by specifying
 * `ng-controller="as propertyName"`.
 *
 * If the current `$controllerProvider` is configured to use globals (via
 * {@link ng.$controllerProvider#allowGlobals `$controllerProvider.allowGlobals()` }), this may
 * also be the name of a globally accessible constructor function (deprecated, not recommended).
 *
 * @example
 * Here is a simple form for editing user contact information. Adding, removing, clearing, and
 * greeting are methods declared on the controller (see source tab). These methods can
 * easily be called from the angular markup. Any changes to the data are automatically reflected
 * in the View without the need for a manual update.
 *
 * Two different declaration styles are included below:
 *
 * * one binds methods and properties directly onto the controller using `this`:
 * `ng-controller="SettingsController1 as settings"`
 * * one injects `$scope` into the controller:
 * `ng-controller="SettingsController2"`
 *
 * The second option is more common in the Angular community, and is generally used in boilerplates
 * and in this guide. However, there are advantages to binding properties directly to the controller
 * and avoiding scope.
 *
 * * Using `controller as` makes it obvious which controller you are accessing in the template when
 * multiple controllers apply to an element.
 * * If you are writing your controllers as classes you have easier access to the properties and
 * methods, which will appear on the scope, from inside the controller code.
 * * Since there is always a `.` in the bindings, you don't have to worry about prototypal
 * inheritance masking primitives.
 *
 * This example demonstrates the `controller as` syntax.
 *
 * <example name="ngControllerAs" module="controllerAsExample">
 *   <file name="index.html">
 *    <div id="ctrl-as-exmpl" ng-controller="SettingsController1 as settings">
 *      <label>Name: <input type="text" ng-model="settings.name"/></label>
 *      <button ng-click="settings.greet()">greet</button><br/>
 *      Contact:
 *      <ul>
 *        <li ng-repeat="contact in settings.contacts">
 *          <select ng-model="contact.type" aria-label="Contact method" id="select_{{$index}}">
 *             <option>phone</option>
 *             <option>email</option>
 *          </select>
 *          <input type="text" ng-model="contact.value" aria-labelledby="select_{{$index}}" />
 *          <button ng-click="settings.clearContact(contact)">clear</button>
 *          <button ng-click="settings.removeContact(contact)" aria-label="Remove">X</button>
 *        </li>
 *        <li><button ng-click="settings.addContact()">add</button></li>
 *     </ul>
 *    </div>
 *   </file>
 *   <file name="app.js">
 *    angular.module('controllerAsExample', [])
 *      .controller('SettingsController1', SettingsController1);
 *
 *    function SettingsController1() {
 *      this.name = 'John Smith';
 *      this.contacts = [
 *        {type: 'phone', value: '408 555 1212'},
 *        {type: 'email', value: 'john.smith@example.org'}
 *      ];
 *    }
 *
 *    SettingsController1.prototype.greet = function() {
 *      alert(this.name);
 *    };
 *
 *    SettingsController1.prototype.addContact = function() {
 *      this.contacts.push({type: 'email', value: 'yourname@example.org'});
 *    };
 *
 *    SettingsController1.prototype.removeContact = function(contactToRemove) {
 *     var index = this.contacts.indexOf(contactToRemove);
 *      this.contacts.splice(index, 1);
 *    };
 *
 *    SettingsController1.prototype.clearContact = function(contact) {
 *      contact.type = 'phone';
 *      contact.value = '';
 *    };
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     it('should check controller as', function() {
 *       var container = element(by.id('ctrl-as-exmpl'));
 *         expect(container.element(by.model('settings.name'))
 *           .getAttribute('value')).toBe('John Smith');
 *
 *       var firstRepeat =
 *           container.element(by.repeater('contact in settings.contacts').row(0));
 *       var secondRepeat =
 *           container.element(by.repeater('contact in settings.contacts').row(1));
 *
 *       expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *           .toBe('408 555 1212');
 *
 *       expect(secondRepeat.element(by.model('contact.value')).getAttribute('value'))
 *           .toBe('john.smith@example.org');
 *
 *       firstRepeat.element(by.buttonText('clear')).click();
 *
 *       expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *           .toBe('');
 *
 *       container.element(by.buttonText('add')).click();
 *
 *       expect(container.element(by.repeater('contact in settings.contacts').row(2))
 *           .element(by.model('contact.value'))
 *           .getAttribute('value'))
 *           .toBe('yourname@example.org');
 *     });
 *   </file>
 * </example>
 *
 * This example demonstrates the "attach to `$scope`" style of controller.
 *
 * <example name="ngController" module="controllerExample">
 *  <file name="index.html">
 *   <div id="ctrl-exmpl" ng-controller="SettingsController2">
 *     <label>Name: <input type="text" ng-model="name"/></label>
 *     <button ng-click="greet()">greet</button><br/>
 *     Contact:
 *     <ul>
 *       <li ng-repeat="contact in contacts">
 *         <select ng-model="contact.type" id="select_{{$index}}">
 *            <option>phone</option>
 *            <option>email</option>
 *         </select>
 *         <input type="text" ng-model="contact.value" aria-labelledby="select_{{$index}}" />
 *         <button ng-click="clearContact(contact)">clear</button>
 *         <button ng-click="removeContact(contact)">X</button>
 *       </li>
 *       <li>[ <button ng-click="addContact()">add</button> ]</li>
 *    </ul>
 *   </div>
 *  </file>
 *  <file name="app.js">
 *   angular.module('controllerExample', [])
 *     .controller('SettingsController2', ['$scope', SettingsController2]);
 *
 *   function SettingsController2($scope) {
 *     $scope.name = 'John Smith';
 *     $scope.contacts = [
 *       {type:'phone', value:'408 555 1212'},
 *       {type:'email', value:'john.smith@example.org'}
 *     ];
 *
 *     $scope.greet = function() {
 *       alert($scope.name);
 *     };
 *
 *     $scope.addContact = function() {
 *       $scope.contacts.push({type:'email', value:'yourname@example.org'});
 *     };
 *
 *     $scope.removeContact = function(contactToRemove) {
 *       var index = $scope.contacts.indexOf(contactToRemove);
 *       $scope.contacts.splice(index, 1);
 *     };
 *
 *     $scope.clearContact = function(contact) {
 *       contact.type = 'phone';
 *       contact.value = '';
 *     };
 *   }
 *  </file>
 *  <file name="protractor.js" type="protractor">
 *    it('should check controller', function() {
 *      var container = element(by.id('ctrl-exmpl'));
 *
 *      expect(container.element(by.model('name'))
 *          .getAttribute('value')).toBe('John Smith');
 *
 *      var firstRepeat =
 *          container.element(by.repeater('contact in contacts').row(0));
 *      var secondRepeat =
 *          container.element(by.repeater('contact in contacts').row(1));
 *
 *      expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *          .toBe('408 555 1212');
 *      expect(secondRepeat.element(by.model('contact.value')).getAttribute('value'))
 *          .toBe('john.smith@example.org');
 *
 *      firstRepeat.element(by.buttonText('clear')).click();
 *
 *      expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *          .toBe('');
 *
 *      container.element(by.buttonText('add')).click();
 *
 *      expect(container.element(by.repeater('contact in contacts').row(2))
 *          .element(by.model('contact.value'))
 *          .getAttribute('value'))
 *          .toBe('yourname@example.org');
 *    });
 *  </file>
 *</example>

 */
var ngControllerDirective = [function() {
  return {
    restrict: 'A',
    scope: true,
    controller: '@',
    priority: 500
  };
}];

/**
 * @ngdoc directive
 * @name ngCsp
 *
 * @restrict A
 * @element ANY
 * @description
 *
 * Angular has some features that can conflict with certain restrictions that are applied when using
 * [CSP (Content Security Policy)](https://developer.mozilla.org/en/Security/CSP) rules.
 *
 * If you intend to implement CSP with these rules then you must tell Angular not to use these
 * features.
 *
 * This is necessary when developing things like Google Chrome Extensions or Universal Windows Apps.
 *
 *
 * The following default rules in CSP affect Angular:
 *
 * * The use of `eval()`, `Function(string)` and similar functions to dynamically create and execute
 * code from strings is forbidden. Angular makes use of this in the {@link $parse} service to
 * provide a 30% increase in the speed of evaluating Angular expressions. (This CSP rule can be
 * disabled with the CSP keyword `unsafe-eval`, but it is generally not recommended as it would
 * weaken the protections offered by CSP.)
 *
 * * The use of inline resources, such as inline `<script>` and `<style>` elements, are forbidden.
 * This prevents apps from injecting custom styles directly into the document. Angular makes use of
 * this to include some CSS rules (e.g. {@link ngCloak} and {@link ngHide}). To make these
 * directives work when a CSP rule is blocking inline styles, you must link to the `angular-csp.css`
 * in your HTML manually. (This CSP rule can be disabled with the CSP keyword `unsafe-inline`, but
 * it is generally not recommended as it would weaken the protections offered by CSP.)
 *
 * If you do not provide `ngCsp` then Angular tries to autodetect if CSP is blocking dynamic code
 * creation from strings (e.g., `unsafe-eval` not specified in CSP header) and automatically
 * deactivates this feature in the {@link $parse} service. This autodetection, however, triggers a
 * CSP error to be logged in the console:
 *
 * ```
 * Refused to evaluate a string as JavaScript because 'unsafe-eval' is not an allowed source of
 * script in the following Content Security Policy directive: "default-src 'self'". Note that
 * 'script-src' was not explicitly set, so 'default-src' is used as a fallback.
 * ```
 *
 * This error is harmless but annoying. To prevent the error from showing up, put the `ngCsp`
 * directive on an element of the HTML document that appears before the `<script>` tag that loads
 * the `angular.js` file.
 *
 * *Note: This directive is only available in the `ng-csp` and `data-ng-csp` attribute form.*
 *
 * You can specify which of the CSP related Angular features should be deactivated by providing
 * a value for the `ng-csp` attribute. The options are as follows:
 *
 * * no-inline-style: this stops Angular from injecting CSS styles into the DOM
 *
 * * no-unsafe-eval: this stops Angular from optimizing $parse with unsafe eval of strings
 *
 * You can use these values in the following combinations:
 *
 *
 * * No declaration means that Angular will assume that you can do inline styles, but it will do
 * a runtime check for unsafe-eval. E.g. `<body>`. This is backwardly compatible with previous
 * versions of Angular.
 *
 * * A simple `ng-csp` (or `data-ng-csp`) attribute will tell Angular to deactivate both inline
 * styles and unsafe eval. E.g. `<body ng-csp>`. This is backwardly compatible with previous
 * versions of Angular.
 *
 * * Specifying only `no-unsafe-eval` tells Angular that we must not use eval, but that we can
 * inject inline styles. E.g. `<body ng-csp="no-unsafe-eval">`.
 *
 * * Specifying only `no-inline-style` tells Angular that we must not inject styles, but that we can
 * run eval - no automatic check for unsafe eval will occur. E.g. `<body ng-csp="no-inline-style">`
 *
 * * Specifying both `no-unsafe-eval` and `no-inline-style` tells Angular that we must not inject
 * styles nor use eval, which is the same as an empty: ng-csp.
 * E.g.`<body ng-csp="no-inline-style;no-unsafe-eval">`
 *
 * @example
 * This example shows how to apply the `ngCsp` directive to the `html` tag.
   ```html
     <!doctype html>
     <html ng-app ng-csp>
     ...
     ...
     </html>
   ```
  * @example
      <!-- Note: the `.csp` suffix in the example name triggers CSP mode in our http server! -->
      <example name="example.csp" module="cspExample" ng-csp="true">
        <file name="index.html">
          <div ng-controller="MainController as ctrl">
            <div>
              <button ng-click="ctrl.inc()" id="inc">Increment</button>
              <span id="counter">
                {{ctrl.counter}}
              </span>
            </div>

            <div>
              <button ng-click="ctrl.evil()" id="evil">Evil</button>
              <span id="evilError">
                {{ctrl.evilError}}
              </span>
            </div>
          </div>
        </file>
        <file name="script.js">
           angular.module('cspExample', [])
             .controller('MainController', function MainController() {
                this.counter = 0;
                this.inc = function() {
                  this.counter++;
                };
                this.evil = function() {
                  try {
                    eval('1+2'); // eslint-disable-line no-eval
                  } catch (e) {
                    this.evilError = e.message;
                  }
                };
              });
        </file>
        <file name="protractor.js" type="protractor">
          var util, webdriver;

          var incBtn = element(by.id('inc'));
          var counter = element(by.id('counter'));
          var evilBtn = element(by.id('evil'));
          var evilError = element(by.id('evilError'));

          function getAndClearSevereErrors() {
            return browser.manage().logs().get('browser').then(function(browserLog) {
              return browserLog.filter(function(logEntry) {
                return logEntry.level.value > webdriver.logging.Level.WARNING.value;
              });
            });
          }

          function clearErrors() {
            getAndClearSevereErrors();
          }

          function expectNoErrors() {
            getAndClearSevereErrors().then(function(filteredLog) {
              expect(filteredLog.length).toEqual(0);
              if (filteredLog.length) {
                console.log('browser console errors: ' + util.inspect(filteredLog));
              }
            });
          }

          function expectError(regex) {
            getAndClearSevereErrors().then(function(filteredLog) {
              var found = false;
              filteredLog.forEach(function(log) {
                if (log.message.match(regex)) {
                  found = true;
                }
              });
              if (!found) {
                throw new Error('expected an error that matches ' + regex);
              }
            });
          }

          beforeEach(function() {
            util = require('util');
            webdriver = require('selenium-webdriver');
          });

          // For now, we only test on Chrome,
          // as Safari does not load the page with Protractor's injected scripts,
          // and Firefox webdriver always disables content security policy (#6358)
          if (browser.params.browser !== 'chrome') {
            return;
          }

          it('should not report errors when the page is loaded', function() {
            // clear errors so we are not dependent on previous tests
            clearErrors();
            // Need to reload the page as the page is already loaded when
            // we come here
            browser.driver.getCurrentUrl().then(function(url) {
              browser.get(url);
            });
            expectNoErrors();
          });

          it('should evaluate expressions', function() {
            expect(counter.getText()).toEqual('0');
            incBtn.click();
            expect(counter.getText()).toEqual('1');
            expectNoErrors();
          });

          it('should throw and report an error when using "eval"', function() {
            evilBtn.click();
            expect(evilError.getText()).toMatch(/Content Security Policy/);
            expectError(/Content Security Policy/);
          });
        </file>
      </example>
  */

// `ngCsp` is not implemented as a proper directive any more, because we need it be processed while
// we bootstrap the app (before `$parse` is instantiated). For this reason, we just have the `csp()`
// fn that looks for the `ng-csp` attribute anywhere in the current doc.

/**
 * @ngdoc directive
 * @name ngClick
 *
 * @description
 * The ngClick directive allows you to specify custom behavior when
 * an element is clicked.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngClick {@link guide/expression Expression} to evaluate upon
 * click. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-click">
     <file name="index.html">
      <button ng-click="count = count + 1" ng-init="count=0">
        Increment
      </button>
      <span>
        count: {{count}}
      </span>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-click', function() {
         expect(element(by.binding('count')).getText()).toMatch('0');
         element(by.css('button')).click();
         expect(element(by.binding('count')).getText()).toMatch('1');
       });
     </file>
   </example>
 */
/*
 * A collection of directives that allows creation of custom event handlers that are defined as
 * angular expressions and are compiled and executed within the current scope.
 */
var ngEventDirectives = {};

// For events that might fire synchronously during DOM manipulation
// we need to execute their event handlers asynchronously using $evalAsync,
// so that they are not executed in an inconsistent state.
var forceAsyncEvents = {
  'blur': true,
  'focus': true
};
forEach(
  'click dblclick mousedown mouseup mouseover mouseout mousemove mouseenter mouseleave keydown keyup keypress submit focus blur copy cut paste'.split(' '),
  function(eventName) {
    var directiveName = directiveNormalize('ng-' + eventName);
    ngEventDirectives[directiveName] = ['$parse', '$rootScope', function($parse, $rootScope) {
      return {
        restrict: 'A',
        compile: function($element, attr) {
          // NOTE:
          // We expose the powerful `$event` object on the scope that provides access to the Window,
          // etc. This is OK, because expressions are not sandboxed any more (and the expression
          // sandbox was never meant to be a security feature anyway).
          var fn = $parse(attr[directiveName]);
          return function ngEventHandler(scope, element) {
            element.on(eventName, function(event) {
              var callback = function() {
                fn(scope, {$event: event});
              };
              if (forceAsyncEvents[eventName] && $rootScope.$$phase) {
                scope.$evalAsync(callback);
              } else {
                scope.$apply(callback);
              }
            });
          };
        }
      };
    }];
  }
);

/**
 * @ngdoc directive
 * @name ngDblclick
 *
 * @description
 * The `ngDblclick` directive allows you to specify custom behavior on a dblclick event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngDblclick {@link guide/expression Expression} to evaluate upon
 * a dblclick. (The Event object is available as `$event`)
 *
 * @example
   <example name="ng-dblclick">
     <file name="index.html">
      <button ng-dblclick="count = count + 1" ng-init="count=0">
        Increment (on double click)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMousedown
 *
 * @description
 * The ngMousedown directive allows you to specify custom behavior on mousedown event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMousedown {@link guide/expression Expression} to evaluate upon
 * mousedown. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mousedown">
     <file name="index.html">
      <button ng-mousedown="count = count + 1" ng-init="count=0">
        Increment (on mouse down)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMouseup
 *
 * @description
 * Specify custom behavior on mouseup event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMouseup {@link guide/expression Expression} to evaluate upon
 * mouseup. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseup">
     <file name="index.html">
      <button ng-mouseup="count = count + 1" ng-init="count=0">
        Increment (on mouse up)
      </button>
      count: {{count}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngMouseover
 *
 * @description
 * Specify custom behavior on mouseover event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMouseover {@link guide/expression Expression} to evaluate upon
 * mouseover. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseover">
     <file name="index.html">
      <button ng-mouseover="count = count + 1" ng-init="count=0">
        Increment (when mouse is over)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMouseenter
 *
 * @description
 * Specify custom behavior on mouseenter event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMouseenter {@link guide/expression Expression} to evaluate upon
 * mouseenter. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseenter">
     <file name="index.html">
      <button ng-mouseenter="count = count + 1" ng-init="count=0">
        Increment (when mouse enters)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMouseleave
 *
 * @description
 * Specify custom behavior on mouseleave event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMouseleave {@link guide/expression Expression} to evaluate upon
 * mouseleave. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseleave">
     <file name="index.html">
      <button ng-mouseleave="count = count + 1" ng-init="count=0">
        Increment (when mouse leaves)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMousemove
 *
 * @description
 * Specify custom behavior on mousemove event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMousemove {@link guide/expression Expression} to evaluate upon
 * mousemove. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mousemove">
     <file name="index.html">
      <button ng-mousemove="count = count + 1" ng-init="count=0">
        Increment (when mouse moves)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngKeydown
 *
 * @description
 * Specify custom behavior on keydown event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngKeydown {@link guide/expression Expression} to evaluate upon
 * keydown. (Event object is available as `$event` and can be interrogated for keyCode, altKey, etc.)
 *
 * @example
   <example name="ng-keydown">
     <file name="index.html">
      <input ng-keydown="count = count + 1" ng-init="count=0">
      key down count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngKeyup
 *
 * @description
 * Specify custom behavior on keyup event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngKeyup {@link guide/expression Expression} to evaluate upon
 * keyup. (Event object is available as `$event` and can be interrogated for keyCode, altKey, etc.)
 *
 * @example
   <example name="ng-keyup">
     <file name="index.html">
       <p>Typing in the input box below updates the key count</p>
       <input ng-keyup="count = count + 1" ng-init="count=0"> key up count: {{count}}

       <p>Typing in the input box below updates the keycode</p>
       <input ng-keyup="event=$event">
       <p>event keyCode: {{ event.keyCode }}</p>
       <p>event altKey: {{ event.altKey }}</p>
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngKeypress
 *
 * @description
 * Specify custom behavior on keypress event.
 *
 * @element ANY
 * @param {expression} ngKeypress {@link guide/expression Expression} to evaluate upon
 * keypress. ({@link guide/expression#-event- Event object is available as `$event`}
 * and can be interrogated for keyCode, altKey, etc.)
 *
 * @example
   <example name="ng-keypress">
     <file name="index.html">
      <input ng-keypress="count = count + 1" ng-init="count=0">
      key press count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngSubmit
 *
 * @description
 * Enables binding angular expressions to onsubmit events.
 *
 * Additionally it prevents the default action (which for form means sending the request to the
 * server and reloading the current page), but only if the form does not contain `action`,
 * `data-action`, or `x-action` attributes.
 *
 * <div class="alert alert-warning">
 * **Warning:** Be careful not to cause "double-submission" by using both the `ngClick` and
 * `ngSubmit` handlers together. See the
 * {@link form#submitting-a-form-and-preventing-the-default-action `form` directive documentation}
 * for a detailed discussion of when `ngSubmit` may be triggered.
 * </div>
 *
 * @element form
 * @priority 0
 * @param {expression} ngSubmit {@link guide/expression Expression} to eval.
 * ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example module="submitExample" name="ng-submit">
     <file name="index.html">
      <script>
        angular.module('submitExample', [])
          .controller('ExampleController', ['$scope', function($scope) {
            $scope.list = [];
            $scope.text = 'hello';
            $scope.submit = function() {
              if ($scope.text) {
                $scope.list.push(this.text);
                $scope.text = '';
              }
            };
          }]);
      </script>
      <form ng-submit="submit()" ng-controller="ExampleController">
        Enter text and hit enter:
        <input type="text" ng-model="text" name="text" />
        <input type="submit" id="submit" value="Submit" />
        <pre>list={{list}}</pre>
      </form>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-submit', function() {
         expect(element(by.binding('list')).getText()).toBe('list=[]');
         element(by.css('#submit')).click();
         expect(element(by.binding('list')).getText()).toContain('hello');
         expect(element(by.model('text')).getAttribute('value')).toBe('');
       });
       it('should ignore empty strings', function() {
         expect(element(by.binding('list')).getText()).toBe('list=[]');
         element(by.css('#submit')).click();
         element(by.css('#submit')).click();
         expect(element(by.binding('list')).getText()).toContain('hello');
        });
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngFocus
 *
 * @description
 * Specify custom behavior on focus event.
 *
 * Note: As the `focus` event is executed synchronously when calling `input.focus()`
 * AngularJS executes the expression using `scope.$evalAsync` if the event is fired
 * during an `$apply` to ensure a consistent state.
 *
 * @element window, input, select, textarea, a
 * @priority 0
 * @param {expression} ngFocus {@link guide/expression Expression} to evaluate upon
 * focus. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
 * See {@link ng.directive:ngClick ngClick}
 */

/**
 * @ngdoc directive
 * @name ngBlur
 *
 * @description
 * Specify custom behavior on blur event.
 *
 * A [blur event](https://developer.mozilla.org/en-US/docs/Web/Events/blur) fires when
 * an element has lost focus.
 *
 * Note: As the `blur` event is executed synchronously also during DOM manipulations
 * (e.g. removing a focussed input),
 * AngularJS executes the expression using `scope.$evalAsync` if the event is fired
 * during an `$apply` to ensure a consistent state.
 *
 * @element window, input, select, textarea, a
 * @priority 0
 * @param {expression} ngBlur {@link guide/expression Expression} to evaluate upon
 * blur. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
 * See {@link ng.directive:ngClick ngClick}
 */

/**
 * @ngdoc directive
 * @name ngCopy
 *
 * @description
 * Specify custom behavior on copy event.
 *
 * @element window, input, select, textarea, a
 * @priority 0
 * @param {expression} ngCopy {@link guide/expression Expression} to evaluate upon
 * copy. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-copy">
     <file name="index.html">
      <input ng-copy="copied=true" ng-init="copied=false; value='copy me'" ng-model="value">
      copied: {{copied}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngCut
 *
 * @description
 * Specify custom behavior on cut event.
 *
 * @element window, input, select, textarea, a
 * @priority 0
 * @param {expression} ngCut {@link guide/expression Expression} to evaluate upon
 * cut. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-cut">
     <file name="index.html">
      <input ng-cut="cut=true" ng-init="cut=false; value='cut me'" ng-model="value">
      cut: {{cut}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngPaste
 *
 * @description
 * Specify custom behavior on paste event.
 *
 * @element window, input, select, textarea, a
 * @priority 0
 * @param {expression} ngPaste {@link guide/expression Expression} to evaluate upon
 * paste. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-paste">
     <file name="index.html">
      <input ng-paste="paste=true" ng-init="paste=false" placeholder='paste here'>
      pasted: {{paste}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngIf
 * @restrict A
 * @multiElement
 *
 * @description
 * The `ngIf` directive removes or recreates a portion of the DOM tree based on an
 * {expression}. If the expression assigned to `ngIf` evaluates to a false
 * value then the element is removed from the DOM, otherwise a clone of the
 * element is reinserted into the DOM.
 *
 * `ngIf` differs from `ngShow` and `ngHide` in that `ngIf` completely removes and recreates the
 * element in the DOM rather than changing its visibility via the `display` css property.  A common
 * case when this difference is significant is when using css selectors that rely on an element's
 * position within the DOM, such as the `:first-child` or `:last-child` pseudo-classes.
 *
 * Note that when an element is removed using `ngIf` its scope is destroyed and a new scope
 * is created when the element is restored.  The scope created within `ngIf` inherits from
 * its parent scope using
 * [prototypal inheritance](https://github.com/angular/angular.js/wiki/Understanding-Scopes#javascript-prototypal-inheritance).
 * An important implication of this is if `ngModel` is used within `ngIf` to bind to
 * a javascript primitive defined in the parent scope. In this case any modifications made to the
 * variable within the child scope will override (hide) the value in the parent scope.
 *
 * Also, `ngIf` recreates elements using their compiled state. An example of this behavior
 * is if an element's class attribute is directly modified after it's compiled, using something like
 * jQuery's `.addClass()` method, and the element is later removed. When `ngIf` recreates the element
 * the added class will be lost because the original compiled state is used to regenerate the element.
 *
 * Additionally, you can provide animations via the `ngAnimate` module to animate the `enter`
 * and `leave` effects.
 *
 * @animations
 * | Animation                        | Occurs                               |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter}  | just after the `ngIf` contents change and a new DOM element is created and injected into the `ngIf` container |
 * | {@link ng.$animate#leave leave}  | just before the `ngIf` contents are removed from the DOM |
 *
 * @element ANY
 * @scope
 * @priority 600
 * @param {expression} ngIf If the {@link guide/expression expression} is falsy then
 *     the element is removed from the DOM tree. If it is truthy a copy of the compiled
 *     element is added to the DOM tree.
 *
 * @example
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-if">
    <file name="index.html">
      <label>Click me: <input type="checkbox" ng-model="checked" ng-init="checked=true" /></label><br/>
      Show when checked:
      <span ng-if="checked" class="animate-if">
        This is removed when the checkbox is unchecked.
      </span>
    </file>
    <file name="animations.css">
      .animate-if {
        background:white;
        border:1px solid black;
        padding:10px;
      }

      .animate-if.ng-enter, .animate-if.ng-leave {
        transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;
      }

      .animate-if.ng-enter,
      .animate-if.ng-leave.ng-leave-active {
        opacity:0;
      }

      .animate-if.ng-leave,
      .animate-if.ng-enter.ng-enter-active {
        opacity:1;
      }
    </file>
  </example>
 */
var ngIfDirective = ['$animate', '$compile', function($animate, $compile) {
  return {
    multiElement: true,
    transclude: 'element',
    priority: 600,
    terminal: true,
    restrict: 'A',
    $$tlb: true,
    link: function($scope, $element, $attr, ctrl, $transclude) {
        var block, childScope, previousElements;
        $scope.$watch($attr.ngIf, function ngIfWatchAction(value) {

          if (value) {
            if (!childScope) {
              $transclude(function(clone, newScope) {
                childScope = newScope;
                clone[clone.length++] = $compile.$$createComment('end ngIf', $attr.ngIf);
                // Note: We only need the first/last node of the cloned nodes.
                // However, we need to keep the reference to the jqlite wrapper as it might be changed later
                // by a directive with templateUrl when its template arrives.
                block = {
                  clone: clone
                };
                $animate.enter(clone, $element.parent(), $element);
              });
            }
          } else {
            if (previousElements) {
              previousElements.remove();
              previousElements = null;
            }
            if (childScope) {
              childScope.$destroy();
              childScope = null;
            }
            if (block) {
              previousElements = getBlockNodes(block.clone);
              $animate.leave(previousElements).done(function(response) {
                if (response !== false) previousElements = null;
              });
              block = null;
            }
          }
        });
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngInclude
 * @restrict ECA
 *
 * @description
 * Fetches, compiles and includes an external HTML fragment.
 *
 * By default, the template URL is restricted to the same domain and protocol as the
 * application document. This is done by calling {@link $sce#getTrustedResourceUrl
 * $sce.getTrustedResourceUrl} on it. To load templates from other domains or protocols
 * you may either {@link ng.$sceDelegateProvider#resourceUrlWhitelist whitelist them} or
 * {@link $sce#trustAsResourceUrl wrap them} as trusted values. Refer to Angular's {@link
 * ng.$sce Strict Contextual Escaping}.
 *
 * In addition, the browser's
 * [Same Origin Policy](https://code.google.com/p/browsersec/wiki/Part2#Same-origin_policy_for_XMLHttpRequest)
 * and [Cross-Origin Resource Sharing (CORS)](http://www.w3.org/TR/cors/)
 * policy may further restrict whether the template is successfully loaded.
 * For example, `ngInclude` won't work for cross-domain requests on all browsers and for `file://`
 * access on some browsers.
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter}  | when the expression changes, on the new include |
 * | {@link ng.$animate#leave leave}  | when the expression changes, on the old include |
 *
 * The enter and leave animation occur concurrently.
 *
 * @scope
 * @priority 400
 *
 * @param {string} ngInclude|src angular expression evaluating to URL. If the source is a string constant,
 *                 make sure you wrap it in **single** quotes, e.g. `src="'myPartialTemplate.html'"`.
 * @param {string=} onload Expression to evaluate when a new partial is loaded.
 *                  <div class="alert alert-warning">
 *                  **Note:** When using onload on SVG elements in IE11, the browser will try to call
 *                  a function with the name on the window element, which will usually throw a
 *                  "function is undefined" error. To fix this, you can instead use `data-onload` or a
 *                  different form that {@link guide/directive#normalization matches} `onload`.
 *                  </div>
   *
 * @param {string=} autoscroll Whether `ngInclude` should call {@link ng.$anchorScroll
 *                  $anchorScroll} to scroll the viewport after the content is loaded.
 *
 *                  - If the attribute is not set, disable scrolling.
 *                  - If the attribute is set without value, enable scrolling.
 *                  - Otherwise enable scrolling only if the expression evaluates to truthy value.
 *
 * @example
  <example module="includeExample" deps="angular-animate.js" animations="true" name="ng-include">
    <file name="index.html">
     <div ng-controller="ExampleController">
       <select ng-model="template" ng-options="t.name for t in templates">
        <option value="">(blank)</option>
       </select>
       url of the template: <code>{{template.url}}</code>
       <hr/>
       <div class="slide-animate-container">
         <div class="slide-animate" ng-include="template.url"></div>
       </div>
     </div>
    </file>
    <file name="script.js">
      angular.module('includeExample', ['ngAnimate'])
        .controller('ExampleController', ['$scope', function($scope) {
          $scope.templates =
            [{ name: 'template1.html', url: 'template1.html'},
             { name: 'template2.html', url: 'template2.html'}];
          $scope.template = $scope.templates[0];
        }]);
     </file>
    <file name="template1.html">
      Content of template1.html
    </file>
    <file name="template2.html">
      Content of template2.html
    </file>
    <file name="animations.css">
      .slide-animate-container {
        position:relative;
        background:white;
        border:1px solid black;
        height:40px;
        overflow:hidden;
      }

      .slide-animate {
        padding:10px;
      }

      .slide-animate.ng-enter, .slide-animate.ng-leave {
        transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;

        position:absolute;
        top:0;
        left:0;
        right:0;
        bottom:0;
        display:block;
        padding:10px;
      }

      .slide-animate.ng-enter {
        top:-50px;
      }
      .slide-animate.ng-enter.ng-enter-active {
        top:0;
      }

      .slide-animate.ng-leave {
        top:0;
      }
      .slide-animate.ng-leave.ng-leave-active {
        top:50px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      var templateSelect = element(by.model('template'));
      var includeElem = element(by.css('[ng-include]'));

      it('should load template1.html', function() {
        expect(includeElem.getText()).toMatch(/Content of template1.html/);
      });

      it('should load template2.html', function() {
        if (browser.params.browser === 'firefox') {
          // Firefox can't handle using selects
          // See https://github.com/angular/protractor/issues/480
          return;
        }
        templateSelect.click();
        templateSelect.all(by.css('option')).get(2).click();
        expect(includeElem.getText()).toMatch(/Content of template2.html/);
      });

      it('should change to blank', function() {
        if (browser.params.browser === 'firefox') {
          // Firefox can't handle using selects
          return;
        }
        templateSelect.click();
        templateSelect.all(by.css('option')).get(0).click();
        expect(includeElem.isPresent()).toBe(false);
      });
    </file>
  </example>
 */


/**
 * @ngdoc event
 * @name ngInclude#$includeContentRequested
 * @eventType emit on the scope ngInclude was declared in
 * @description
 * Emitted every time the ngInclude content is requested.
 *
 * @param {Object} angularEvent Synthetic event object.
 * @param {String} src URL of content to load.
 */


/**
 * @ngdoc event
 * @name ngInclude#$includeContentLoaded
 * @eventType emit on the current ngInclude scope
 * @description
 * Emitted every time the ngInclude content is reloaded.
 *
 * @param {Object} angularEvent Synthetic event object.
 * @param {String} src URL of content to load.
 */


/**
 * @ngdoc event
 * @name ngInclude#$includeContentError
 * @eventType emit on the scope ngInclude was declared in
 * @description
 * Emitted when a template HTTP request yields an erroneous response (status < 200 || status > 299)
 *
 * @param {Object} angularEvent Synthetic event object.
 * @param {String} src URL of content to load.
 */
var ngIncludeDirective = ['$templateRequest', '$anchorScroll', '$animate',
                  function($templateRequest,   $anchorScroll,   $animate) {
  return {
    restrict: 'ECA',
    priority: 400,
    terminal: true,
    transclude: 'element',
    controller: angular.noop,
    compile: function(element, attr) {
      var srcExp = attr.ngInclude || attr.src,
          onloadExp = attr.onload || '',
          autoScrollExp = attr.autoscroll;

      return function(scope, $element, $attr, ctrl, $transclude) {
        var changeCounter = 0,
            currentScope,
            previousElement,
            currentElement;

        var cleanupLastIncludeContent = function() {
          if (previousElement) {
            previousElement.remove();
            previousElement = null;
          }
          if (currentScope) {
            currentScope.$destroy();
            currentScope = null;
          }
          if (currentElement) {
            $animate.leave(currentElement).done(function(response) {
              if (response !== false) previousElement = null;
            });
            previousElement = currentElement;
            currentElement = null;
          }
        };

        scope.$watch(srcExp, function ngIncludeWatchAction(src) {
          var afterAnimation = function(response) {
            if (response !== false && isDefined(autoScrollExp) &&
              (!autoScrollExp || scope.$eval(autoScrollExp))) {
                $anchorScroll();
            }
          };
          var thisChangeId = ++changeCounter;

          if (src) {
            //set the 2nd param to true to ignore the template request error so that the inner
            //contents and scope can be cleaned up.
            $templateRequest(src, true).then(function(response) {
              if (scope.$$destroyed) return;

              if (thisChangeId !== changeCounter) return;
              var newScope = scope.$new();
              ctrl.template = response;

              // Note: This will also link all children of ng-include that were contained in the original
              // html. If that content contains controllers, ... they could pollute/change the scope.
              // However, using ng-include on an element with additional content does not make sense...
              // Note: We can't remove them in the cloneAttchFn of $transclude as that
              // function is called before linking the content, which would apply child
              // directives to non existing elements.
              var clone = $transclude(newScope, function(clone) {
                cleanupLastIncludeContent();
                $animate.enter(clone, null, $element).done(afterAnimation);
              });

              currentScope = newScope;
              currentElement = clone;

              currentScope.$emit('$includeContentLoaded', src);
              scope.$eval(onloadExp);
            }, function() {
              if (scope.$$destroyed) return;

              if (thisChangeId === changeCounter) {
                cleanupLastIncludeContent();
                scope.$emit('$includeContentError', src);
              }
            });
            scope.$emit('$includeContentRequested', src);
          } else {
            cleanupLastIncludeContent();
            ctrl.template = null;
          }
        });
      };
    }
  };
}];

// This directive is called during the $transclude call of the first `ngInclude` directive.
// It will replace and compile the content of the element with the loaded template.
// We need this directive so that the element content is already filled when
// the link function of another directive on the same element as ngInclude
// is called.
var ngIncludeFillContentDirective = ['$compile',
  function($compile) {
    return {
      restrict: 'ECA',
      priority: -400,
      require: 'ngInclude',
      link: function(scope, $element, $attr, ctrl) {
        if (toString.call($element[0]).match(/SVG/)) {
          // WebKit: https://bugs.webkit.org/show_bug.cgi?id=135698 --- SVG elements do not
          // support innerHTML, so detect this here and try to generate the contents
          // specially.
          $element.empty();
          $compile(jqLiteBuildFragment(ctrl.template, window.document).childNodes)(scope,
              function namespaceAdaptedClone(clone) {
            $element.append(clone);
          }, {futureParentElement: $element});
          return;
        }

        $element.html(ctrl.template);
        $compile($element.contents())(scope);
      }
    };
  }];

/**
 * @ngdoc directive
 * @name ngInit
 * @restrict AC
 *
 * @description
 * The `ngInit` directive allows you to evaluate an expression in the
 * current scope.
 *
 * <div class="alert alert-danger">
 * This directive can be abused to add unnecessary amounts of logic into your templates.
 * There are only a few appropriate uses of `ngInit`, such as for aliasing special properties of
 * {@link ng.directive:ngRepeat `ngRepeat`}, as seen in the demo below; and for injecting data via
 * server side scripting. Besides these few cases, you should use {@link guide/controller controllers}
 * rather than `ngInit` to initialize values on a scope.
 * </div>
 *
 * <div class="alert alert-warning">
 * **Note**: If you have assignment in `ngInit` along with a {@link ng.$filter `filter`}, make
 * sure you have parentheses to ensure correct operator precedence:
 * <pre class="prettyprint">
 * `<div ng-init="test1 = ($index | toString)"></div>`
 * </pre>
 * </div>
 *
 * @priority 450
 *
 * @element ANY
 * @param {expression} ngInit {@link guide/expression Expression} to eval.
 *
 * @example
   <example module="initExample" name="ng-init">
     <file name="index.html">
   <script>
     angular.module('initExample', [])
       .controller('ExampleController', ['$scope', function($scope) {
         $scope.list = [['a', 'b'], ['c', 'd']];
       }]);
   </script>
   <div ng-controller="ExampleController">
     <div ng-repeat="innerList in list" ng-init="outerIndex = $index">
       <div ng-repeat="value in innerList" ng-init="innerIndex = $index">
          <span class="example-init">list[ {{outerIndex}} ][ {{innerIndex}} ] = {{value}};</span>
       </div>
     </div>
   </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should alias index positions', function() {
         var elements = element.all(by.css('.example-init'));
         expect(elements.get(0).getText()).toBe('list[ 0 ][ 0 ] = a;');
         expect(elements.get(1).getText()).toBe('list[ 0 ][ 1 ] = b;');
         expect(elements.get(2).getText()).toBe('list[ 1 ][ 0 ] = c;');
         expect(elements.get(3).getText()).toBe('list[ 1 ][ 1 ] = d;');
       });
     </file>
   </example>
 */
var ngInitDirective = ngDirective({
  priority: 450,
  compile: function() {
    return {
      pre: function(scope, element, attrs) {
        scope.$eval(attrs.ngInit);
      }
    };
  }
});

/**
 * @ngdoc directive
 * @name ngList
 *
 * @description
 * Text input that converts between a delimited string and an array of strings. The default
 * delimiter is a comma followed by a space - equivalent to `ng-list=", "`. You can specify a custom
 * delimiter as the value of the `ngList` attribute - for example, `ng-list=" | "`.
 *
 * The behaviour of the directive is affected by the use of the `ngTrim` attribute.
 * * If `ngTrim` is set to `"false"` then whitespace around both the separator and each
 *   list item is respected. This implies that the user of the directive is responsible for
 *   dealing with whitespace but also allows you to use whitespace as a delimiter, such as a
 *   tab or newline character.
 * * Otherwise whitespace around the delimiter is ignored when splitting (although it is respected
 *   when joining the list items back together) and whitespace around each list item is stripped
 *   before it is added to the model.
 *
 * ### Example with Validation
 *
 * <example name="ngList-directive" module="listExample">
 *   <file name="app.js">
 *      angular.module('listExample', [])
 *        .controller('ExampleController', ['$scope', function($scope) {
 *          $scope.names = ['morpheus', 'neo', 'trinity'];
 *        }]);
 *   </file>
 *   <file name="index.html">
 *    <form name="myForm" ng-controller="ExampleController">
 *      <label>List: <input name="namesInput" ng-model="names" ng-list required></label>
 *      <span role="alert">
 *        <span class="error" ng-show="myForm.namesInput.$error.required">
 *        Required!</span>
 *      </span>
 *      <br>
 *      <tt>names = {{names}}</tt><br/>
 *      <tt>myForm.namesInput.$valid = {{myForm.namesInput.$valid}}</tt><br/>
 *      <tt>myForm.namesInput.$error = {{myForm.namesInput.$error}}</tt><br/>
 *      <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
 *      <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
 *     </form>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     var listInput = element(by.model('names'));
 *     var names = element(by.exactBinding('names'));
 *     var valid = element(by.binding('myForm.namesInput.$valid'));
 *     var error = element(by.css('span.error'));
 *
 *     it('should initialize to model', function() {
 *       expect(names.getText()).toContain('["morpheus","neo","trinity"]');
 *       expect(valid.getText()).toContain('true');
 *       expect(error.getCssValue('display')).toBe('none');
 *     });
 *
 *     it('should be invalid if empty', function() {
 *       listInput.clear();
 *       listInput.sendKeys('');
 *
 *       expect(names.getText()).toContain('');
 *       expect(valid.getText()).toContain('false');
 *       expect(error.getCssValue('display')).not.toBe('none');
 *     });
 *   </file>
 * </example>
 *
 * ### Example - splitting on newline
 * <example name="ngList-directive-newlines">
 *   <file name="index.html">
 *    <textarea ng-model="list" ng-list="&#10;" ng-trim="false"></textarea>
 *    <pre>{{ list | json }}</pre>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     it("should split the text by newlines", function() {
 *       var listInput = element(by.model('list'));
 *       var output = element(by.binding('list | json'));
 *       listInput.sendKeys('abc\ndef\nghi');
 *       expect(output.getText()).toContain('[\n  "abc",\n  "def",\n  "ghi"\n]');
 *     });
 *   </file>
 * </example>
 *
 * @element input
 * @param {string=} ngList optional delimiter that should be used to split the value.
 */
var ngListDirective = function() {
  return {
    restrict: 'A',
    priority: 100,
    require: 'ngModel',
    link: function(scope, element, attr, ctrl) {
      var ngList = attr.ngList || ', ';
      var trimValues = attr.ngTrim !== 'false';
      var separator = trimValues ? trim(ngList) : ngList;

      var parse = function(viewValue) {
        // If the viewValue is invalid (say required but empty) it will be `undefined`
        if (isUndefined(viewValue)) return;

        var list = [];

        if (viewValue) {
          forEach(viewValue.split(separator), function(value) {
            if (value) list.push(trimValues ? trim(value) : value);
          });
        }

        return list;
      };

      ctrl.$parsers.push(parse);
      ctrl.$formatters.push(function(value) {
        if (isArray(value)) {
          return value.join(ngList);
        }

        return undefined;
      });

      // Override the standard $isEmpty because an empty array means the input is empty.
      ctrl.$isEmpty = function(value) {
        return !value || !value.length;
      };
    }
  };
};

/* global VALID_CLASS: true,
  INVALID_CLASS: true,
  PRISTINE_CLASS: true,
  DIRTY_CLASS: true,
  UNTOUCHED_CLASS: true,
  TOUCHED_CLASS: true,
  PENDING_CLASS: true,
  addSetValidityMethod: true,
  setupValidity: true,
  defaultModelOptions: false
*/


var VALID_CLASS = 'ng-valid',
    INVALID_CLASS = 'ng-invalid',
    PRISTINE_CLASS = 'ng-pristine',
    DIRTY_CLASS = 'ng-dirty',
    UNTOUCHED_CLASS = 'ng-untouched',
    TOUCHED_CLASS = 'ng-touched',
    EMPTY_CLASS = 'ng-empty',
    NOT_EMPTY_CLASS = 'ng-not-empty';

var ngModelMinErr = minErr('ngModel');

/**
 * @ngdoc type
 * @name ngModel.NgModelController
 *
 * @property {*} $viewValue The actual value from the control's view. For `input` elements, this is a
 * String. See {@link ngModel.NgModelController#$setViewValue} for information about when the $viewValue
 * is set.
 *
 * @property {*} $modelValue The value in the model that the control is bound to.
 *
 * @property {Array.<Function>} $parsers Array of functions to execute, as a pipeline, whenever
 *  the control updates the ngModelController with a new {@link ngModel.NgModelController#$viewValue
    `$viewValue`} from the DOM, usually via user input.
    See {@link ngModel.NgModelController#$setViewValue `$setViewValue()`} for a detailed lifecycle explanation.
    Note that the `$parsers` are not called when the bound ngModel expression changes programmatically.

  The functions are called in array order, each passing
    its return value through to the next. The last return value is forwarded to the
    {@link ngModel.NgModelController#$validators `$validators`} collection.

  Parsers are used to sanitize / convert the {@link ngModel.NgModelController#$viewValue
    `$viewValue`}.

  Returning `undefined` from a parser means a parse error occurred. In that case,
    no {@link ngModel.NgModelController#$validators `$validators`} will run and the `ngModel`
    will be set to `undefined` unless {@link ngModelOptions `ngModelOptions.allowInvalid`}
    is set to `true`. The parse error is stored in `ngModel.$error.parse`.

  This simple example shows a parser that would convert text input value to lowercase:
 * ```js
 * function parse(value) {
 *   if (value) {
 *     return value.toLowerCase();
 *   }
 * }
 * ngModelController.$parsers.push(parse);
 * ```

 *
 * @property {Array.<Function>} $formatters Array of functions to execute, as a pipeline, whenever
    the bound ngModel expression changes programmatically. The `$formatters` are not called when the
    value of the control is changed by user interaction.

  Formatters are used to format / convert the {@link ngModel.NgModelController#$modelValue
    `$modelValue`} for display in the control.

  The functions are called in reverse array order, each passing the value through to the
    next. The last return value is used as the actual DOM value.

  This simple example shows a formatter that would convert the model value to uppercase:

 * ```js
 * function format(value) {
 *   if (value) {
 *     return value.toUpperCase();
 *   }
 * }
 * ngModel.$formatters.push(format);
 * ```
 *
 * @property {Object.<string, function>} $validators A collection of validators that are applied
 *      whenever the model value changes. The key value within the object refers to the name of the
 *      validator while the function refers to the validation operation. The validation operation is
 *      provided with the model value as an argument and must return a true or false value depending
 *      on the response of that validation.
 *
 * ```js
 * ngModel.$validators.validCharacters = function(modelValue, viewValue) {
 *   var value = modelValue || viewValue;
 *   return /[0-9]+/.test(value) &&
 *          /[a-z]+/.test(value) &&
 *          /[A-Z]+/.test(value) &&
 *          /\W+/.test(value);
 * };
 * ```
 *
 * @property {Object.<string, function>} $asyncValidators A collection of validations that are expected to
 *      perform an asynchronous validation (e.g. a HTTP request). The validation function that is provided
 *      is expected to return a promise when it is run during the model validation process. Once the promise
 *      is delivered then the validation status will be set to true when fulfilled and false when rejected.
 *      When the asynchronous validators are triggered, each of the validators will run in parallel and the model
 *      value will only be updated once all validators have been fulfilled. As long as an asynchronous validator
 *      is unfulfilled, its key will be added to the controllers `$pending` property. Also, all asynchronous validators
 *      will only run once all synchronous validators have passed.
 *
 * Please note that if $http is used then it is important that the server returns a success HTTP response code
 * in order to fulfill the validation and a status level of `4xx` in order to reject the validation.
 *
 * ```js
 * ngModel.$asyncValidators.uniqueUsername = function(modelValue, viewValue) {
 *   var value = modelValue || viewValue;
 *
 *   // Lookup user by username
 *   return $http.get('/api/users/' + value).
 *      then(function resolved() {
 *        //username exists, this means validation fails
 *        return $q.reject('exists');
 *      }, function rejected() {
 *        //username does not exist, therefore this validation passes
 *        return true;
 *      });
 * };
 * ```
 *
 * @property {Array.<Function>} $viewChangeListeners Array of functions to execute whenever the
 *     view value has changed. It is called with no arguments, and its return value is ignored.
 *     This can be used in place of additional $watches against the model value.
 *
 * @property {Object} $error An object hash with all failing validator ids as keys.
 * @property {Object} $pending An object hash with all pending validator ids as keys.
 *
 * @property {boolean} $untouched True if control has not lost focus yet.
 * @property {boolean} $touched True if control has lost focus.
 * @property {boolean} $pristine True if user has not interacted with the control yet.
 * @property {boolean} $dirty True if user has already interacted with the control.
 * @property {boolean} $valid True if there is no error.
 * @property {boolean} $invalid True if at least one error on the control.
 * @property {string} $name The name attribute of the control.
 *
 * @description
 *
 * `NgModelController` provides API for the {@link ngModel `ngModel`} directive.
 * The controller contains services for data-binding, validation, CSS updates, and value formatting
 * and parsing. It purposefully does not contain any logic which deals with DOM rendering or
 * listening to DOM events.
 * Such DOM related logic should be provided by other directives which make use of
 * `NgModelController` for data-binding to control elements.
 * Angular provides this DOM logic for most {@link input `input`} elements.
 * At the end of this page you can find a {@link ngModel.NgModelController#custom-control-example
 * custom control example} that uses `ngModelController` to bind to `contenteditable` elements.
 *
 * @example
 * ### Custom Control Example
 * This example shows how to use `NgModelController` with a custom control to achieve
 * data-binding. Notice how different directives (`contenteditable`, `ng-model`, and `required`)
 * collaborate together to achieve the desired result.
 *
 * `contenteditable` is an HTML5 attribute, which tells the browser to let the element
 * contents be edited in place by the user.
 *
 * We are using the {@link ng.service:$sce $sce} service here and include the {@link ngSanitize $sanitize}
 * module to automatically remove "bad" content like inline event listener (e.g. `<span onclick="...">`).
 * However, as we are using `$sce` the model can still decide to provide unsafe content if it marks
 * that content using the `$sce` service.
 *
 * <example name="NgModelController" module="customControl" deps="angular-sanitize.js">
    <file name="style.css">
      [contenteditable] {
        border: 1px solid black;
        background-color: white;
        min-height: 20px;
      }

      .ng-invalid {
        border: 1px solid red;
      }

    </file>
    <file name="script.js">
      angular.module('customControl', ['ngSanitize']).
        directive('contenteditable', ['$sce', function($sce) {
          return {
            restrict: 'A', // only activate on element attribute
            require: '?ngModel', // get a hold of NgModelController
            link: function(scope, element, attrs, ngModel) {
              if (!ngModel) return; // do nothing if no ng-model

              // Specify how UI should be updated
              ngModel.$render = function() {
                element.html($sce.getTrustedHtml(ngModel.$viewValue || ''));
              };

              // Listen for change events to enable binding
              element.on('blur keyup change', function() {
                scope.$evalAsync(read);
              });
              read(); // initialize

              // Write data to the model
              function read() {
                var html = element.html();
                // When we clear the content editable the browser leaves a <br> behind
                // If strip-br attribute is provided then we strip this out
                if (attrs.stripBr && html === '<br>') {
                  html = '';
                }
                ngModel.$setViewValue(html);
              }
            }
          };
        }]);
    </file>
    <file name="index.html">
      <form name="myForm">
       <div contenteditable
            name="myWidget" ng-model="userContent"
            strip-br="true"
            required>Change me!</div>
        <span ng-show="myForm.myWidget.$error.required">Required!</span>
       <hr>
       <textarea ng-model="userContent" aria-label="Dynamic textarea"></textarea>
      </form>
    </file>
    <file name="protractor.js" type="protractor">
    it('should data-bind and become invalid', function() {
      if (browser.params.browser === 'safari' || browser.params.browser === 'firefox') {
        // SafariDriver can't handle contenteditable
        // and Firefox driver can't clear contenteditables very well
        return;
      }
      var contentEditable = element(by.css('[contenteditable]'));
      var content = 'Change me!';

      expect(contentEditable.getText()).toEqual(content);

      contentEditable.clear();
      contentEditable.sendKeys(protractor.Key.BACK_SPACE);
      expect(contentEditable.getText()).toEqual('');
      expect(contentEditable.getAttribute('class')).toMatch(/ng-invalid-required/);
    });
    </file>
 * </example>
 *
 *
 */
NgModelController.$inject = ['$scope', '$exceptionHandler', '$attrs', '$element', '$parse', '$animate', '$timeout', '$q', '$interpolate'];
function NgModelController($scope, $exceptionHandler, $attr, $element, $parse, $animate, $timeout, $q, $interpolate) {
  this.$viewValue = Number.NaN;
  this.$modelValue = Number.NaN;
  this.$$rawModelValue = undefined; // stores the parsed modelValue / model set from scope regardless of validity.
  this.$validators = {};
  this.$asyncValidators = {};
  this.$parsers = [];
  this.$formatters = [];
  this.$viewChangeListeners = [];
  this.$untouched = true;
  this.$touched = false;
  this.$pristine = true;
  this.$dirty = false;
  this.$valid = true;
  this.$invalid = false;
  this.$error = {}; // keep invalid keys here
  this.$$success = {}; // keep valid keys here
  this.$pending = undefined; // keep pending keys here
  this.$name = $interpolate($attr.name || '', false)($scope);
  this.$$parentForm = nullFormCtrl;
  this.$options = defaultModelOptions;

  this.$$parsedNgModel = $parse($attr.ngModel);
  this.$$parsedNgModelAssign = this.$$parsedNgModel.assign;
  this.$$ngModelGet = this.$$parsedNgModel;
  this.$$ngModelSet = this.$$parsedNgModelAssign;
  this.$$pendingDebounce = null;
  this.$$parserValid = undefined;

  this.$$currentValidationRunId = 0;

  this.$$scope = $scope;
  this.$$attr = $attr;
  this.$$element = $element;
  this.$$animate = $animate;
  this.$$timeout = $timeout;
  this.$$parse = $parse;
  this.$$q = $q;
  this.$$exceptionHandler = $exceptionHandler;

  setupValidity(this);
  setupModelWatcher(this);
}

NgModelController.prototype = {
  $$initGetterSetters: function() {
    if (this.$options.getOption('getterSetter')) {
      var invokeModelGetter = this.$$parse(this.$$attr.ngModel + '()'),
          invokeModelSetter = this.$$parse(this.$$attr.ngModel + '($$$p)');

      this.$$ngModelGet = function($scope) {
        var modelValue = this.$$parsedNgModel($scope);
        if (isFunction(modelValue)) {
          modelValue = invokeModelGetter($scope);
        }
        return modelValue;
      };
      this.$$ngModelSet = function($scope, newValue) {
        if (isFunction(this.$$parsedNgModel($scope))) {
          invokeModelSetter($scope, {$$$p: newValue});
        } else {
          this.$$parsedNgModelAssign($scope, newValue);
        }
      };
    } else if (!this.$$parsedNgModel.assign) {
      throw ngModelMinErr('nonassign', 'Expression \'{0}\' is non-assignable. Element: {1}',
          this.$$attr.ngModel, startingTag(this.$$element));
    }
  },


  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$render
   *
   * @description
   * Called when the view needs to be updated. It is expected that the user of the ng-model
   * directive will implement this method.
   *
   * The `$render()` method is invoked in the following situations:
   *
   * * `$rollbackViewValue()` is called.  If we are rolling back the view value to the last
   *   committed value then `$render()` is called to update the input control.
   * * The value referenced by `ng-model` is changed programmatically and both the `$modelValue` and
   *   the `$viewValue` are different from last time.
   *
   * Since `ng-model` does not do a deep watch, `$render()` is only invoked if the values of
   * `$modelValue` and `$viewValue` are actually different from their previous values. If `$modelValue`
   * or `$viewValue` are objects (rather than a string or number) then `$render()` will not be
   * invoked if you only change a property on the objects.
   */
  $render: noop,

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$isEmpty
   *
   * @description
   * This is called when we need to determine if the value of an input is empty.
   *
   * For instance, the required directive does this to work out if the input has data or not.
   *
   * The default `$isEmpty` function checks whether the value is `undefined`, `''`, `null` or `NaN`.
   *
   * You can override this for input directives whose concept of being empty is different from the
   * default. The `checkboxInputType` directive does this because in its case a value of `false`
   * implies empty.
   *
   * @param {*} value The value of the input to check for emptiness.
   * @returns {boolean} True if `value` is "empty".
   */
  $isEmpty: function(value) {
    // eslint-disable-next-line no-self-compare
    return isUndefined(value) || value === '' || value === null || value !== value;
  },

  $$updateEmptyClasses: function(value) {
    if (this.$isEmpty(value)) {
      this.$$animate.removeClass(this.$$element, NOT_EMPTY_CLASS);
      this.$$animate.addClass(this.$$element, EMPTY_CLASS);
    } else {
      this.$$animate.removeClass(this.$$element, EMPTY_CLASS);
      this.$$animate.addClass(this.$$element, NOT_EMPTY_CLASS);
    }
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setPristine
   *
   * @description
   * Sets the control to its pristine state.
   *
   * This method can be called to remove the `ng-dirty` class and set the control to its pristine
   * state (`ng-pristine` class). A model is considered to be pristine when the control
   * has not been changed from when first compiled.
   */
  $setPristine: function() {
    this.$dirty = false;
    this.$pristine = true;
    this.$$animate.removeClass(this.$$element, DIRTY_CLASS);
    this.$$animate.addClass(this.$$element, PRISTINE_CLASS);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setDirty
   *
   * @description
   * Sets the control to its dirty state.
   *
   * This method can be called to remove the `ng-pristine` class and set the control to its dirty
   * state (`ng-dirty` class). A model is considered to be dirty when the control has been changed
   * from when first compiled.
   */
  $setDirty: function() {
    this.$dirty = true;
    this.$pristine = false;
    this.$$animate.removeClass(this.$$element, PRISTINE_CLASS);
    this.$$animate.addClass(this.$$element, DIRTY_CLASS);
    this.$$parentForm.$setDirty();
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setUntouched
   *
   * @description
   * Sets the control to its untouched state.
   *
   * This method can be called to remove the `ng-touched` class and set the control to its
   * untouched state (`ng-untouched` class). Upon compilation, a model is set as untouched
   * by default, however this function can be used to restore that state if the model has
   * already been touched by the user.
   */
  $setUntouched: function() {
    this.$touched = false;
    this.$untouched = true;
    this.$$animate.setClass(this.$$element, UNTOUCHED_CLASS, TOUCHED_CLASS);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setTouched
   *
   * @description
   * Sets the control to its touched state.
   *
   * This method can be called to remove the `ng-untouched` class and set the control to its
   * touched state (`ng-touched` class). A model is considered to be touched when the user has
   * first focused the control element and then shifted focus away from the control (blur event).
   */
  $setTouched: function() {
    this.$touched = true;
    this.$untouched = false;
    this.$$animate.setClass(this.$$element, TOUCHED_CLASS, UNTOUCHED_CLASS);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$rollbackViewValue
   *
   * @description
   * Cancel an update and reset the input element's value to prevent an update to the `$modelValue`,
   * which may be caused by a pending debounced event or because the input is waiting for some
   * future event.
   *
   * If you have an input that uses `ng-model-options` to set up debounced updates or updates that
   * depend on special events such as `blur`, there can be a period when the `$viewValue` is out of
   * sync with the ngModel's `$modelValue`.
   *
   * In this case, you can use `$rollbackViewValue()` to manually cancel the debounced / future update
   * and reset the input to the last committed view value.
   *
   * It is also possible that you run into difficulties if you try to update the ngModel's `$modelValue`
   * programmatically before these debounced/future events have resolved/occurred, because Angular's
   * dirty checking mechanism is not able to tell whether the model has actually changed or not.
   *
   * The `$rollbackViewValue()` method should be called before programmatically changing the model of an
   * input which may have such events pending. This is important in order to make sure that the
   * input field will be updated with the new model value and any pending operations are cancelled.
   *
   * <example name="ng-model-cancel-update" module="cancel-update-example">
   *   <file name="app.js">
   *     angular.module('cancel-update-example', [])
   *
   *     .controller('CancelUpdateController', ['$scope', function($scope) {
   *       $scope.model = {value1: '', value2: ''};
   *
   *       $scope.setEmpty = function(e, value, rollback) {
   *         if (e.keyCode === 27) {
   *           e.preventDefault();
   *           if (rollback) {
   *             $scope.myForm[value].$rollbackViewValue();
   *           }
   *           $scope.model[value] = '';
   *         }
   *       };
   *     }]);
   *   </file>
   *   <file name="index.html">
   *     <div ng-controller="CancelUpdateController">
   *       <p>Both of these inputs are only updated if they are blurred. Hitting escape should
   *       empty them. Follow these steps and observe the difference:</p>
   *       <ol>
   *         <li>Type something in the input. You will see that the model is not yet updated</li>
   *         <li>Press the Escape key.
   *           <ol>
   *             <li> In the first example, nothing happens, because the model is already '', and no
   *             update is detected. If you blur the input, the model will be set to the current view.
   *             </li>
   *             <li> In the second example, the pending update is cancelled, and the input is set back
   *             to the last committed view value (''). Blurring the input does nothing.
   *             </li>
   *           </ol>
   *         </li>
   *       </ol>
   *
   *       <form name="myForm" ng-model-options="{ updateOn: 'blur' }">
   *         <div>
   *           <p id="inputDescription1">Without $rollbackViewValue():</p>
   *           <input name="value1" aria-describedby="inputDescription1" ng-model="model.value1"
   *                  ng-keydown="setEmpty($event, 'value1')">
   *           value1: "{{ model.value1 }}"
   *         </div>
   *
   *         <div>
   *           <p id="inputDescription2">With $rollbackViewValue():</p>
   *           <input name="value2" aria-describedby="inputDescription2" ng-model="model.value2"
   *                  ng-keydown="setEmpty($event, 'value2', true)">
   *           value2: "{{ model.value2 }}"
   *         </div>
   *       </form>
   *     </div>
   *   </file>
       <file name="style.css">
          div {
            display: table-cell;
          }
          div:nth-child(1) {
            padding-right: 30px;
          }

        </file>
   * </example>
   */
  $rollbackViewValue: function() {
    this.$$timeout.cancel(this.$$pendingDebounce);
    this.$viewValue = this.$$lastCommittedViewValue;
    this.$render();
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$validate
   *
   * @description
   * Runs each of the registered validators (first synchronous validators and then
   * asynchronous validators).
   * If the validity changes to invalid, the model will be set to `undefined`,
   * unless {@link ngModelOptions `ngModelOptions.allowInvalid`} is `true`.
   * If the validity changes to valid, it will set the model to the last available valid
   * `$modelValue`, i.e. either the last parsed value or the last value set from the scope.
   */
  $validate: function() {
    // ignore $validate before model is initialized
    if (isNumberNaN(this.$modelValue)) {
      return;
    }

    var viewValue = this.$$lastCommittedViewValue;
    // Note: we use the $$rawModelValue as $modelValue might have been
    // set to undefined during a view -> model update that found validation
    // errors. We can't parse the view here, since that could change
    // the model although neither viewValue nor the model on the scope changed
    var modelValue = this.$$rawModelValue;

    var prevValid = this.$valid;
    var prevModelValue = this.$modelValue;

    var allowInvalid = this.$options.getOption('allowInvalid');

    var that = this;
    this.$$runValidators(modelValue, viewValue, function(allValid) {
      // If there was no change in validity, don't update the model
      // This prevents changing an invalid modelValue to undefined
      if (!allowInvalid && prevValid !== allValid) {
        // Note: Don't check this.$valid here, as we could have
        // external validators (e.g. calculated on the server),
        // that just call $setValidity and need the model value
        // to calculate their validity.
        that.$modelValue = allValid ? modelValue : undefined;

        if (that.$modelValue !== prevModelValue) {
          that.$$writeModelToScope();
        }
      }
    });
  },

  $$runValidators: function(modelValue, viewValue, doneCallback) {
    this.$$currentValidationRunId++;
    var localValidationRunId = this.$$currentValidationRunId;
    var that = this;

    // check parser error
    if (!processParseErrors()) {
      validationDone(false);
      return;
    }
    if (!processSyncValidators()) {
      validationDone(false);
      return;
    }
    processAsyncValidators();

    function processParseErrors() {
      var errorKey = that.$$parserName || 'parse';
      if (isUndefined(that.$$parserValid)) {
        setValidity(errorKey, null);
      } else {
        if (!that.$$parserValid) {
          forEach(that.$validators, function(v, name) {
            setValidity(name, null);
          });
          forEach(that.$asyncValidators, function(v, name) {
            setValidity(name, null);
          });
        }
        // Set the parse error last, to prevent unsetting it, should a $validators key == parserName
        setValidity(errorKey, that.$$parserValid);
        return that.$$parserValid;
      }
      return true;
    }

    function processSyncValidators() {
      var syncValidatorsValid = true;
      forEach(that.$validators, function(validator, name) {
        var result = Boolean(validator(modelValue, viewValue));
        syncValidatorsValid = syncValidatorsValid && result;
        setValidity(name, result);
      });
      if (!syncValidatorsValid) {
        forEach(that.$asyncValidators, function(v, name) {
          setValidity(name, null);
        });
        return false;
      }
      return true;
    }

    function processAsyncValidators() {
      var validatorPromises = [];
      var allValid = true;
      forEach(that.$asyncValidators, function(validator, name) {
        var promise = validator(modelValue, viewValue);
        if (!isPromiseLike(promise)) {
          throw ngModelMinErr('nopromise',
            'Expected asynchronous validator to return a promise but got \'{0}\' instead.', promise);
        }
        setValidity(name, undefined);
        validatorPromises.push(promise.then(function() {
          setValidity(name, true);
        }, function() {
          allValid = false;
          setValidity(name, false);
        }));
      });
      if (!validatorPromises.length) {
        validationDone(true);
      } else {
        that.$$q.all(validatorPromises).then(function() {
          validationDone(allValid);
        }, noop);
      }
    }

    function setValidity(name, isValid) {
      if (localValidationRunId === that.$$currentValidationRunId) {
        that.$setValidity(name, isValid);
      }
    }

    function validationDone(allValid) {
      if (localValidationRunId === that.$$currentValidationRunId) {

        doneCallback(allValid);
      }
    }
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$commitViewValue
   *
   * @description
   * Commit a pending update to the `$modelValue`.
   *
   * Updates may be pending by a debounced event or because the input is waiting for a some future
   * event defined in `ng-model-options`. this method is rarely needed as `NgModelController`
   * usually handles calling this in response to input events.
   */
  $commitViewValue: function() {
    var viewValue = this.$viewValue;

    this.$$timeout.cancel(this.$$pendingDebounce);

    // If the view value has not changed then we should just exit, except in the case where there is
    // a native validator on the element. In this case the validation state may have changed even though
    // the viewValue has stayed empty.
    if (this.$$lastCommittedViewValue === viewValue && (viewValue !== '' || !this.$$hasNativeValidators)) {
      return;
    }
    this.$$updateEmptyClasses(viewValue);
    this.$$lastCommittedViewValue = viewValue;

    // change to dirty
    if (this.$pristine) {
      this.$setDirty();
    }
    this.$$parseAndValidate();
  },

  $$parseAndValidate: function() {
    var viewValue = this.$$lastCommittedViewValue;
    var modelValue = viewValue;
    var that = this;

    this.$$parserValid = isUndefined(modelValue) ? undefined : true;

    if (this.$$parserValid) {
      for (var i = 0; i < this.$parsers.length; i++) {
        modelValue = this.$parsers[i](modelValue);
        if (isUndefined(modelValue)) {
          this.$$parserValid = false;
          break;
        }
      }
    }
    if (isNumberNaN(this.$modelValue)) {
      // this.$modelValue has not been touched yet...
      this.$modelValue = this.$$ngModelGet(this.$$scope);
    }
    var prevModelValue = this.$modelValue;
    var allowInvalid = this.$options.getOption('allowInvalid');
    this.$$rawModelValue = modelValue;

    if (allowInvalid) {
      this.$modelValue = modelValue;
      writeToModelIfNeeded();
    }

    // Pass the $$lastCommittedViewValue here, because the cached viewValue might be out of date.
    // This can happen if e.g. $setViewValue is called from inside a parser
    this.$$runValidators(modelValue, this.$$lastCommittedViewValue, function(allValid) {
      if (!allowInvalid) {
        // Note: Don't check this.$valid here, as we could have
        // external validators (e.g. calculated on the server),
        // that just call $setValidity and need the model value
        // to calculate their validity.
        that.$modelValue = allValid ? modelValue : undefined;
        writeToModelIfNeeded();
      }
    });

    function writeToModelIfNeeded() {
      if (that.$modelValue !== prevModelValue) {
        that.$$writeModelToScope();
      }
    }
  },

  $$writeModelToScope: function() {
    this.$$ngModelSet(this.$$scope, this.$modelValue);
    forEach(this.$viewChangeListeners, function(listener) {
      try {
        listener();
      } catch (e) {
        // eslint-disable-next-line no-invalid-this
        this.$$exceptionHandler(e);
      }
    }, this);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setViewValue
   *
   * @description
   * Update the view value.
   *
   * This method should be called when a control wants to change the view value; typically,
   * this is done from within a DOM event handler. For example, the {@link ng.directive:input input}
   * directive calls it when the value of the input changes and {@link ng.directive:select select}
   * calls it when an option is selected.
   *
   * When `$setViewValue` is called, the new `value` will be staged for committing through the `$parsers`
   * and `$validators` pipelines. If there are no special {@link ngModelOptions} specified then the staged
   * value is sent directly for processing through the `$parsers` pipeline. After this, the `$validators` and
   * `$asyncValidators` are called and the value is applied to `$modelValue`.
   * Finally, the value is set to the **expression** specified in the `ng-model` attribute and
   * all the registered change listeners, in the `$viewChangeListeners` list are called.
   *
   * In case the {@link ng.directive:ngModelOptions ngModelOptions} directive is used with `updateOn`
   * and the `default` trigger is not listed, all those actions will remain pending until one of the
   * `updateOn` events is triggered on the DOM element.
   * All these actions will be debounced if the {@link ng.directive:ngModelOptions ngModelOptions}
   * directive is used with a custom debounce for this particular event.
   * Note that a `$digest` is only triggered once the `updateOn` events are fired, or if `debounce`
   * is specified, once the timer runs out.
   *
   * When used with standard inputs, the view value will always be a string (which is in some cases
   * parsed into another type, such as a `Date` object for `input[date]`.)
   * However, custom controls might also pass objects to this method. In this case, we should make
   * a copy of the object before passing it to `$setViewValue`. This is because `ngModel` does not
   * perform a deep watch of objects, it only looks for a change of identity. If you only change
   * the property of the object then ngModel will not realize that the object has changed and
   * will not invoke the `$parsers` and `$validators` pipelines. For this reason, you should
   * not change properties of the copy once it has been passed to `$setViewValue`.
   * Otherwise you may cause the model value on the scope to change incorrectly.
   *
   * <div class="alert alert-info">
   * In any case, the value passed to the method should always reflect the current value
   * of the control. For example, if you are calling `$setViewValue` for an input element,
   * you should pass the input DOM value. Otherwise, the control and the scope model become
   * out of sync. It's also important to note that `$setViewValue` does not call `$render` or change
   * the control's DOM value in any way. If we want to change the control's DOM value
   * programmatically, we should update the `ngModel` scope expression. Its new value will be
   * picked up by the model controller, which will run it through the `$formatters`, `$render` it
   * to update the DOM, and finally call `$validate` on it.
   * </div>
   *
   * @param {*} value value from the view.
   * @param {string} trigger Event that triggered the update.
   */
  $setViewValue: function(value, trigger) {
    this.$viewValue = value;
    if (this.$options.getOption('updateOnDefault')) {
      this.$$debounceViewValueCommit(trigger);
    }
  },

  $$debounceViewValueCommit: function(trigger) {
    var debounceDelay = this.$options.getOption('debounce');

    if (isNumber(debounceDelay[trigger])) {
      debounceDelay = debounceDelay[trigger];
    } else if (isNumber(debounceDelay['default'])) {
      debounceDelay = debounceDelay['default'];
    }

    this.$$timeout.cancel(this.$$pendingDebounce);
    var that = this;
    if (debounceDelay > 0) { // this fails if debounceDelay is an object
      this.$$pendingDebounce = this.$$timeout(function() {
        that.$commitViewValue();
      }, debounceDelay);
    } else if (this.$$scope.$root.$$phase) {
      this.$commitViewValue();
    } else {
      this.$$scope.$apply(function() {
        that.$commitViewValue();
      });
    }
  },

  /**
   * @ngdoc method
   *
   * @name ngModel.NgModelController#$overrideModelOptions
   *
   * @description
   *
   * Override the current model options settings programmatically.
   *
   * The previous `ModelOptions` value will not be modified. Instead, a
   * new `ModelOptions` object will inherit from the previous one overriding
   * or inheriting settings that are defined in the given parameter.
   *
   * See {@link ngModelOptions} for information about what options can be specified
   * and how model option inheritance works.
   *
   * @param {Object} options a hash of settings to override the previous options
   *
   */
  $overrideModelOptions: function(options) {
    this.$options = this.$options.createChild(options);
  }
};

function setupModelWatcher(ctrl) {
  // model -> value
  // Note: we cannot use a normal scope.$watch as we want to detect the following:
  // 1. scope value is 'a'
  // 2. user enters 'b'
  // 3. ng-change kicks in and reverts scope value to 'a'
  //    -> scope value did not change since the last digest as
  //       ng-change executes in apply phase
  // 4. view should be changed back to 'a'
  ctrl.$$scope.$watch(function ngModelWatch() {
    var modelValue = ctrl.$$ngModelGet(ctrl.$$scope);

    // if scope model value and ngModel value are out of sync
    // TODO(perf): why not move this to the action fn?
    if (modelValue !== ctrl.$modelValue &&
       // checks for NaN is needed to allow setting the model to NaN when there's an asyncValidator
        // eslint-disable-next-line no-self-compare
       (ctrl.$modelValue === ctrl.$modelValue || modelValue === modelValue)
    ) {
      ctrl.$modelValue = ctrl.$$rawModelValue = modelValue;
      ctrl.$$parserValid = undefined;

      var formatters = ctrl.$formatters,
          idx = formatters.length;

      var viewValue = modelValue;
      while (idx--) {
        viewValue = formatters[idx](viewValue);
      }
      if (ctrl.$viewValue !== viewValue) {
        ctrl.$$updateEmptyClasses(viewValue);
        ctrl.$viewValue = ctrl.$$lastCommittedViewValue = viewValue;
        ctrl.$render();

        // It is possible that model and view value have been updated during render
        ctrl.$$runValidators(ctrl.$modelValue, ctrl.$viewValue, noop);
      }
    }

    return modelValue;
  });
}

/**
 * @ngdoc method
 * @name ngModel.NgModelController#$setValidity
 *
 * @description
 * Change the validity state, and notify the form.
 *
 * This method can be called within $parsers/$formatters or a custom validation implementation.
 * However, in most cases it should be sufficient to use the `ngModel.$validators` and
 * `ngModel.$asyncValidators` collections which will call `$setValidity` automatically.
 *
 * @param {string} validationErrorKey Name of the validator. The `validationErrorKey` will be assigned
 *        to either `$error[validationErrorKey]` or `$pending[validationErrorKey]`
 *        (for unfulfilled `$asyncValidators`), so that it is available for data-binding.
 *        The `validationErrorKey` should be in camelCase and will get converted into dash-case
 *        for class name. Example: `myError` will result in `ng-valid-my-error` and `ng-invalid-my-error`
 *        class and can be bound to as  `{{someForm.someControl.$error.myError}}` .
 * @param {boolean} isValid Whether the current state is valid (true), invalid (false), pending (undefined),
 *                          or skipped (null). Pending is used for unfulfilled `$asyncValidators`.
 *                          Skipped is used by Angular when validators do not run because of parse errors and
 *                          when `$asyncValidators` do not run because any of the `$validators` failed.
 */
addSetValidityMethod({
  clazz: NgModelController,
  set: function(object, property) {
    object[property] = true;
  },
  unset: function(object, property) {
    delete object[property];
  }
});


/**
 * @ngdoc directive
 * @name ngModel
 *
 * @element input
 * @priority 1
 *
 * @description
 * The `ngModel` directive binds an `input`,`select`, `textarea` (or custom form control) to a
 * property on the scope using {@link ngModel.NgModelController NgModelController},
 * which is created and exposed by this directive.
 *
 * `ngModel` is responsible for:
 *
 * - Binding the view into the model, which other directives such as `input`, `textarea` or `select`
 *   require.
 * - Providing validation behavior (i.e. required, number, email, url).
 * - Keeping the state of the control (valid/invalid, dirty/pristine, touched/untouched, validation errors).
 * - Setting related css classes on the element (`ng-valid`, `ng-invalid`, `ng-dirty`, `ng-pristine`, `ng-touched`,
 *   `ng-untouched`, `ng-empty`, `ng-not-empty`) including animations.
 * - Registering the control with its parent {@link ng.directive:form form}.
 *
 * Note: `ngModel` will try to bind to the property given by evaluating the expression on the
 * current scope. If the property doesn't already exist on this scope, it will be created
 * implicitly and added to the scope.
 *
 * For best practices on using `ngModel`, see:
 *
 *  - [Understanding Scopes](https://github.com/angular/angular.js/wiki/Understanding-Scopes)
 *
 * For basic examples, how to use `ngModel`, see:
 *
 *  - {@link ng.directive:input input}
 *    - {@link input[text] text}
 *    - {@link input[checkbox] checkbox}
 *    - {@link input[radio] radio}
 *    - {@link input[number] number}
 *    - {@link input[email] email}
 *    - {@link input[url] url}
 *    - {@link input[date] date}
 *    - {@link input[datetime-local] datetime-local}
 *    - {@link input[time] time}
 *    - {@link input[month] month}
 *    - {@link input[week] week}
 *  - {@link ng.directive:select select}
 *  - {@link ng.directive:textarea textarea}
 *
 * # Complex Models (objects or collections)
 *
 * By default, `ngModel` watches the model by reference, not value. This is important to know when
 * binding inputs to models that are objects (e.g. `Date`) or collections (e.g. arrays). If only properties of the
 * object or collection change, `ngModel` will not be notified and so the input will not be  re-rendered.
 *
 * The model must be assigned an entirely new object or collection before a re-rendering will occur.
 *
 * Some directives have options that will cause them to use a custom `$watchCollection` on the model expression
 * - for example, `ngOptions` will do so when a `track by` clause is included in the comprehension expression or
 * if the select is given the `multiple` attribute.
 *
 * The `$watchCollection()` method only does a shallow comparison, meaning that changing properties deeper than the
 * first level of the object (or only changing the properties of an item in the collection if it's an array) will still
 * not trigger a re-rendering of the model.
 *
 * # CSS classes
 * The following CSS classes are added and removed on the associated input/select/textarea element
 * depending on the validity of the model.
 *
 *  - `ng-valid`: the model is valid
 *  - `ng-invalid`: the model is invalid
 *  - `ng-valid-[key]`: for each valid key added by `$setValidity`
 *  - `ng-invalid-[key]`: for each invalid key added by `$setValidity`
 *  - `ng-pristine`: the control hasn't been interacted with yet
 *  - `ng-dirty`: the control has been interacted with
 *  - `ng-touched`: the control has been blurred
 *  - `ng-untouched`: the control hasn't been blurred
 *  - `ng-pending`: any `$asyncValidators` are unfulfilled
 *  - `ng-empty`: the view does not contain a value or the value is deemed "empty", as defined
 *     by the {@link ngModel.NgModelController#$isEmpty} method
 *  - `ng-not-empty`: the view contains a non-empty value
 *
 * Keep in mind that ngAnimate can detect each of these classes when added and removed.
 *
 * ## Animation Hooks
 *
 * Animations within models are triggered when any of the associated CSS classes are added and removed
 * on the input element which is attached to the model. These classes include: `.ng-pristine`, `.ng-dirty`,
 * `.ng-invalid` and `.ng-valid` as well as any other validations that are performed on the model itself.
 * The animations that are triggered within ngModel are similar to how they work in ngClass and
 * animations can be hooked into using CSS transitions, keyframes as well as JS animations.
 *
 * The following example shows a simple way to utilize CSS transitions to style an input element
 * that has been rendered as invalid after it has been validated:
 *
 * <pre>
 * //be sure to include ngAnimate as a module to hook into more
 * //advanced animations
 * .my-input {
 *   transition:0.5s linear all;
 *   background: white;
 * }
 * .my-input.ng-invalid {
 *   background: red;
 *   color:white;
 * }
 * </pre>
 *
 * @example
 * <example deps="angular-animate.js" animations="true" fixBase="true" module="inputExample" name="ng-model">
     <file name="index.html">
       <script>
        angular.module('inputExample', [])
          .controller('ExampleController', ['$scope', function($scope) {
            $scope.val = '1';
          }]);
       </script>
       <style>
         .my-input {
           transition:all linear 0.5s;
           background: transparent;
         }
         .my-input.ng-invalid {
           color:white;
           background: red;
         }
       </style>
       <p id="inputDescription">
        Update input to see transitions when valid/invalid.
        Integer is a valid value.
       </p>
       <form name="testForm" ng-controller="ExampleController">
         <input ng-model="val" ng-pattern="/^\d+$/" name="anim" class="my-input"
                aria-describedby="inputDescription" />
       </form>
     </file>
 * </example>
 *
 * ## Binding to a getter/setter
 *
 * Sometimes it's helpful to bind `ngModel` to a getter/setter function.  A getter/setter is a
 * function that returns a representation of the model when called with zero arguments, and sets
 * the internal state of a model when called with an argument. It's sometimes useful to use this
 * for models that have an internal representation that's different from what the model exposes
 * to the view.
 *
 * <div class="alert alert-success">
 * **Best Practice:** It's best to keep getters fast because Angular is likely to call them more
 * frequently than other parts of your code.
 * </div>
 *
 * You use this behavior by adding `ng-model-options="{ getterSetter: true }"` to an element that
 * has `ng-model` attached to it. You can also add `ng-model-options="{ getterSetter: true }"` to
 * a `<form>`, which will enable this behavior for all `<input>`s within it. See
 * {@link ng.directive:ngModelOptions `ngModelOptions`} for more.
 *
 * The following example shows how to use `ngModel` with a getter/setter:
 *
 * @example
 * <example name="ngModel-getter-setter" module="getterSetterExample">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <form name="userForm">
           <label>Name:
             <input type="text" name="userName"
                    ng-model="user.name"
                    ng-model-options="{ getterSetter: true }" />
           </label>
         </form>
         <pre>user.name = <span ng-bind="user.name()"></span></pre>
       </div>
     </file>
     <file name="app.js">
       angular.module('getterSetterExample', [])
         .controller('ExampleController', ['$scope', function($scope) {
           var _name = 'Brian';
           $scope.user = {
             name: function(newName) {
              // Note that newName can be undefined for two reasons:
              // 1. Because it is called as a getter and thus called with no arguments
              // 2. Because the property should actually be set to undefined. This happens e.g. if the
              //    input is invalid
              return arguments.length ? (_name = newName) : _name;
             }
           };
         }]);
     </file>
 * </example>
 */
var ngModelDirective = ['$rootScope', function($rootScope) {
  return {
    restrict: 'A',
    require: ['ngModel', '^?form', '^?ngModelOptions'],
    controller: NgModelController,
    // Prelink needs to run before any input directive
    // so that we can set the NgModelOptions in NgModelController
    // before anyone else uses it.
    priority: 1,
    compile: function ngModelCompile(element) {
      // Setup initial state of the control
      element.addClass(PRISTINE_CLASS).addClass(UNTOUCHED_CLASS).addClass(VALID_CLASS);

      return {
        pre: function ngModelPreLink(scope, element, attr, ctrls) {
          var modelCtrl = ctrls[0],
              formCtrl = ctrls[1] || modelCtrl.$$parentForm,
              optionsCtrl = ctrls[2];

          if (optionsCtrl) {
            modelCtrl.$options = optionsCtrl.$options;
          }

          modelCtrl.$$initGetterSetters();

          // notify others, especially parent forms
          formCtrl.$addControl(modelCtrl);

          attr.$observe('name', function(newValue) {
            if (modelCtrl.$name !== newValue) {
              modelCtrl.$$parentForm.$$renameControl(modelCtrl, newValue);
            }
          });

          scope.$on('$destroy', function() {
            modelCtrl.$$parentForm.$removeControl(modelCtrl);
          });
        },
        post: function ngModelPostLink(scope, element, attr, ctrls) {
          var modelCtrl = ctrls[0];
          if (modelCtrl.$options.getOption('updateOn')) {
            element.on(modelCtrl.$options.getOption('updateOn'), function(ev) {
              modelCtrl.$$debounceViewValueCommit(ev && ev.type);
            });
          }

          function setTouched() {
            modelCtrl.$setTouched();
          }

          element.on('blur', function() {
            if (modelCtrl.$touched) return;

            if ($rootScope.$$phase) {
              scope.$evalAsync(setTouched);
            } else {
              scope.$apply(setTouched);
            }
          });
        }
      };
    }
  };
}];

/* exported defaultModelOptions */
var defaultModelOptions;
var DEFAULT_REGEXP = /(\s+|^)default(\s+|$)/;

/**
 * @ngdoc type
 * @name ModelOptions
 * @description
 * A container for the options set by the {@link ngModelOptions} directive
 */
function ModelOptions(options) {
  this.$$options = options;
}

ModelOptions.prototype = {

  /**
   * @ngdoc method
   * @name ModelOptions#getOption
   * @param {string} name the name of the option to retrieve
   * @returns {*} the value of the option
   * @description
   * Returns the value of the given option
   */
  getOption: function(name) {
    return this.$$options[name];
  },

  /**
   * @ngdoc method
   * @name ModelOptions#createChild
   * @param {Object} options a hash of options for the new child that will override the parent's options
   * @return {ModelOptions} a new `ModelOptions` object initialized with the given options.
   */
  createChild: function(options) {
    var inheritAll = false;

    // make a shallow copy
    options = extend({}, options);

    // Inherit options from the parent if specified by the value `"$inherit"`
    forEach(options, /* @this */ function(option, key) {
      if (option === '$inherit') {
        if (key === '*') {
          inheritAll = true;
        } else {
          options[key] = this.$$options[key];
          // `updateOn` is special so we must also inherit the `updateOnDefault` option
          if (key === 'updateOn') {
            options.updateOnDefault = this.$$options.updateOnDefault;
          }
        }
      } else {
        if (key === 'updateOn') {
          // If the `updateOn` property contains the `default` event then we have to remove
          // it from the event list and set the `updateOnDefault` flag.
          options.updateOnDefault = false;
          options[key] = trim(option.replace(DEFAULT_REGEXP, function() {
            options.updateOnDefault = true;
            return ' ';
          }));
        }
      }
    }, this);

    if (inheritAll) {
      // We have a property of the form: `"*": "$inherit"`
      delete options['*'];
      defaults(options, this.$$options);
    }

    // Finally add in any missing defaults
    defaults(options, defaultModelOptions.$$options);

    return new ModelOptions(options);
  }
};


defaultModelOptions = new ModelOptions({
  updateOn: '',
  updateOnDefault: true,
  debounce: 0,
  getterSetter: false,
  allowInvalid: false,
  timezone: null
});


/**
 * @ngdoc directive
 * @name ngModelOptions
 *
 * @description
 * This directive allows you to modify the behaviour of {@link ngModel} directives within your
 * application. You can specify an `ngModelOptions` directive on any element. All {@link ngModel}
 * directives will use the options of their nearest `ngModelOptions` ancestor.
 *
 * The `ngModelOptions` settings are found by evaluating the value of the attribute directive as
 * an Angular expression. This expression should evaluate to an object, whose properties contain
 * the settings. For example: `<div "ng-model-options"="{ debounce: 100 }"`.
 *
 * ## Inheriting Options
 *
 * You can specify that an `ngModelOptions` setting should be inherited from a parent `ngModelOptions`
 * directive by giving it the value of `"$inherit"`.
 * Then it will inherit that setting from the first `ngModelOptions` directive found by traversing up the
 * DOM tree. If there is no ancestor element containing an `ngModelOptions` directive then default settings
 * will be used.
 *
 * For example given the following fragment of HTML
 *
 *
 * ```html
 * <div ng-model-options="{ allowInvalid: true, debounce: 200 }">
 *   <form ng-model-options="{ updateOn: 'blur', allowInvalid: '$inherit' }">
 *     <input ng-model-options="{ updateOn: 'default', allowInvalid: '$inherit' }" />
 *   </form>
 * </div>
 * ```
 *
 * the `input` element will have the following settings
 *
 * ```js
 * { allowInvalid: true, updateOn: 'default', debounce: 0 }
 * ```
 *
 * Notice that the `debounce` setting was not inherited and used the default value instead.
 *
 * You can specify that all undefined settings are automatically inherited from an ancestor by
 * including a property with key of `"*"` and value of `"$inherit"`.
 *
 * For example given the following fragment of HTML
 *
 *
 * ```html
 * <div ng-model-options="{ allowInvalid: true, debounce: 200 }">
 *   <form ng-model-options="{ updateOn: 'blur', "*": '$inherit' }">
 *     <input ng-model-options="{ updateOn: 'default', "*": '$inherit' }" />
 *   </form>
 * </div>
 * ```
 *
 * the `input` element will have the following settings
 *
 * ```js
 * { allowInvalid: true, updateOn: 'default', debounce: 200 }
 * ```
 *
 * Notice that the `debounce` setting now inherits the value from the outer `<div>` element.
 *
 * If you are creating a reusable component then you should be careful when using `"*": "$inherit"`
 * since you may inadvertently inherit a setting in the future that changes the behavior of your component.
 *
 *
 * ## Triggering and debouncing model updates
 *
 * The `updateOn` and `debounce` properties allow you to specify a custom list of events that will
 * trigger a model update and/or a debouncing delay so that the actual update only takes place when
 * a timer expires; this timer will be reset after another change takes place.
 *
 * Given the nature of `ngModelOptions`, the value displayed inside input fields in the view might
 * be different from the value in the actual model. This means that if you update the model you
 * should also invoke {@link ngModel.NgModelController#$rollbackViewValue} on the relevant input field in
 * order to make sure it is synchronized with the model and that any debounced action is canceled.
 *
 * The easiest way to reference the control's {@link ngModel.NgModelController#$rollbackViewValue}
 * method is by making sure the input is placed inside a form that has a `name` attribute. This is
 * important because `form` controllers are published to the related scope under the name in their
 * `name` attribute.
 *
 * Any pending changes will take place immediately when an enclosing form is submitted via the
 * `submit` event. Note that `ngClick` events will occur before the model is updated. Use `ngSubmit`
 * to have access to the updated model.
 *
 * The following example shows how to override immediate updates. Changes on the inputs within the
 * form will update the model only when the control loses focus (blur event). If `escape` key is
 * pressed while the input field is focused, the value is reset to the value in the current model.
 *
 * <example name="ngModelOptions-directive-blur" module="optionsExample">
 *   <file name="index.html">
 *     <div ng-controller="ExampleController">
 *       <form name="userForm">
 *         <label>
 *           Name:
 *           <input type="text" name="userName"
 *                  ng-model="user.name"
 *                  ng-model-options="{ updateOn: 'blur' }"
 *                  ng-keyup="cancel($event)" />
 *         </label><br />
 *         <label>
 *           Other data:
 *           <input type="text" ng-model="user.data" />
 *         </label><br />
 *       </form>
 *       <pre>user.name = <span ng-bind="user.name"></span></pre>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *     angular.module('optionsExample', [])
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         $scope.user = { name: 'say', data: '' };
 *
 *         $scope.cancel = function(e) {
 *           if (e.keyCode === 27) {
 *             $scope.userForm.userName.$rollbackViewValue();
 *           }
 *         };
 *       }]);
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     var model = element(by.binding('user.name'));
 *     var input = element(by.model('user.name'));
 *     var other = element(by.model('user.data'));
 *
 *     it('should allow custom events', function() {
 *       input.sendKeys(' hello');
 *       input.click();
 *       expect(model.getText()).toEqual('say');
 *       other.click();
 *       expect(model.getText()).toEqual('say hello');
 *     });
 *
 *     it('should $rollbackViewValue when model changes', function() {
 *       input.sendKeys(' hello');
 *       expect(input.getAttribute('value')).toEqual('say hello');
 *       input.sendKeys(protractor.Key.ESCAPE);
 *       expect(input.getAttribute('value')).toEqual('say');
 *       other.click();
 *       expect(model.getText()).toEqual('say');
 *     });
 *   </file>
 * </example>
 *
 * The next example shows how to debounce model changes. Model will be updated only 1 sec after last change.
 * If the `Clear` button is pressed, any debounced action is canceled and the value becomes empty.
 *
 * <example name="ngModelOptions-directive-debounce" module="optionsExample">
 *   <file name="index.html">
 *     <div ng-controller="ExampleController">
 *       <form name="userForm">
 *         Name:
 *         <input type="text" name="userName"
 *                ng-model="user.name"
 *                ng-model-options="{ debounce: 1000 }" />
 *         <button ng-click="userForm.userName.$rollbackViewValue(); user.name=''">Clear</button><br />
 *       </form>
 *       <pre>user.name = <span ng-bind="user.name"></span></pre>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *     angular.module('optionsExample', [])
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         $scope.user = { name: 'say' };
 *       }]);
 *   </file>
 * </example>
 *
 * ## Model updates and validation
 *
 * The default behaviour in `ngModel` is that the model value is set to `undefined` when the
 * validation determines that the value is invalid. By setting the `allowInvalid` property to true,
 * the model will still be updated even if the value is invalid.
 *
 *
 * ## Connecting to the scope
 *
 * By setting the `getterSetter` property to true you are telling ngModel that the `ngModel` expression
 * on the scope refers to a "getter/setter" function rather than the value itself.
 *
 * The following example shows how to bind to getter/setters:
 *
 * <example name="ngModelOptions-directive-getter-setter" module="getterSetterExample">
 *   <file name="index.html">
 *     <div ng-controller="ExampleController">
 *       <form name="userForm">
 *         <label>
 *           Name:
 *           <input type="text" name="userName"
 *                  ng-model="user.name"
 *                  ng-model-options="{ getterSetter: true }" />
 *         </label>
 *       </form>
 *       <pre>user.name = <span ng-bind="user.name()"></span></pre>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *     angular.module('getterSetterExample', [])
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         var _name = 'Brian';
 *         $scope.user = {
 *           name: function(newName) {
 *             return angular.isDefined(newName) ? (_name = newName) : _name;
 *           }
 *         };
 *       }]);
 *   </file>
 * </example>
 *
 *
 * ## Specifying timezones
 *
 * You can specify the timezone that date/time input directives expect by providing its name in the
 * `timezone` property.
 *
 * @param {Object} ngModelOptions options to apply to {@link ngModel} directives on this element and
 *   and its descendents. Valid keys are:
 *   - `updateOn`: string specifying which event should the input be bound to. You can set several
 *     events using an space delimited list. There is a special event called `default` that
 *     matches the default events belonging to the control.
 *   - `debounce`: integer value which contains the debounce model update value in milliseconds. A
 *     value of 0 triggers an immediate update. If an object is supplied instead, you can specify a
 *     custom value for each event. For example:
 *     ```
 *     ng-model-options="{
 *       updateOn: 'default blur',
 *       debounce: { 'default': 500, 'blur': 0 }
 *     }"
 *     ```
 *   - `allowInvalid`: boolean value which indicates that the model can be set with values that did
 *     not validate correctly instead of the default behavior of setting the model to undefined.
 *   - `getterSetter`: boolean value which determines whether or not to treat functions bound to
 *     `ngModel` as getters/setters.
 *   - `timezone`: Defines the timezone to be used to read/write the `Date` instance in the model for
 *     `<input type="date" />`, `<input type="time" />`, ... . It understands UTC/GMT and the
 *     continental US time zone abbreviations, but for general use, use a time zone offset, for
 *     example, `'+0430'` (4 hours, 30 minutes east of the Greenwich meridian)
 *     If not specified, the timezone of the browser will be used.
 *
 */
var ngModelOptionsDirective = function() {
  NgModelOptionsController.$inject = ['$attrs', '$scope'];
  function NgModelOptionsController($attrs, $scope) {
    this.$$attrs = $attrs;
    this.$$scope = $scope;
  }
  NgModelOptionsController.prototype = {
    $onInit: function() {
      var parentOptions = this.parentCtrl ? this.parentCtrl.$options : defaultModelOptions;
      var modelOptionsDefinition = this.$$scope.$eval(this.$$attrs.ngModelOptions);

      this.$options = parentOptions.createChild(modelOptionsDefinition);
    }
  };

  return {
    restrict: 'A',
    // ngModelOptions needs to run before ngModel and input directives
    priority: 10,
    require: {parentCtrl: '?^^ngModelOptions'},
    bindToController: true,
    controller: NgModelOptionsController
  };
};


// shallow copy over values from `src` that are not already specified on `dst`
function defaults(dst, src) {
  forEach(src, function(value, key) {
    if (!isDefined(dst[key])) {
      dst[key] = value;
    }
  });
}

/**
 * @ngdoc directive
 * @name ngNonBindable
 * @restrict AC
 * @priority 1000
 *
 * @description
 * The `ngNonBindable` directive tells Angular not to compile or bind the contents of the current
 * DOM element. This is useful if the element contains what appears to be Angular directives and
 * bindings but which should be ignored by Angular. This could be the case if you have a site that
 * displays snippets of code, for instance.
 *
 * @element ANY
 *
 * @example
 * In this example there are two locations where a simple interpolation binding (`{{}}`) is present,
 * but the one wrapped in `ngNonBindable` is left alone.
 *
 * @example
    <example name="ng-non-bindable">
      <file name="index.html">
        <div>Normal: {{1 + 2}}</div>
        <div ng-non-bindable>Ignored: {{1 + 2}}</div>
      </file>
      <file name="protractor.js" type="protractor">
       it('should check ng-non-bindable', function() {
         expect(element(by.binding('1 + 2')).getText()).toContain('3');
         expect(element.all(by.css('div')).last().getText()).toMatch(/1 \+ 2/);
       });
      </file>
    </example>
 */
var ngNonBindableDirective = ngDirective({ terminal: true, priority: 1000 });

/* exported ngOptionsDirective */

/* global jqLiteRemove */

var ngOptionsMinErr = minErr('ngOptions');

/**
 * @ngdoc directive
 * @name ngOptions
 * @restrict A
 *
 * @description
 *
 * The `ngOptions` attribute can be used to dynamically generate a list of `<option>`
 * elements for the `<select>` element using the array or object obtained by evaluating the
 * `ngOptions` comprehension expression.
 *
 * In many cases, {@link ng.directive:ngRepeat ngRepeat} can be used on `<option>` elements instead of
 * `ngOptions` to achieve a similar result. However, `ngOptions` provides some benefits:
 * - more flexibility in how the `<select>`'s model is assigned via the `select` **`as`** part of the
 * comprehension expression
 * - reduced memory consumption by not creating a new scope for each repeated instance
 * - increased render speed by creating the options in a documentFragment instead of individually
 *
 * When an item in the `<select>` menu is selected, the array element or object property
 * represented by the selected option will be bound to the model identified by the `ngModel`
 * directive.
 *
 * Optionally, a single hard-coded `<option>` element, with the value set to an empty string, can
 * be nested into the `<select>` element. This element will then represent the `null` or "not selected"
 * option. See example below for demonstration.
 *
 * ## Complex Models (objects or collections)
 *
 * By default, `ngModel` watches the model by reference, not value. This is important to know when
 * binding the select to a model that is an object or a collection.
 *
 * One issue occurs if you want to preselect an option. For example, if you set
 * the model to an object that is equal to an object in your collection, `ngOptions` won't be able to set the selection,
 * because the objects are not identical. So by default, you should always reference the item in your collection
 * for preselections, e.g.: `$scope.selected = $scope.collection[3]`.
 *
 * Another solution is to use a `track by` clause, because then `ngOptions` will track the identity
 * of the item not by reference, but by the result of the `track by` expression. For example, if your
 * collection items have an id property, you would `track by item.id`.
 *
 * A different issue with objects or collections is that ngModel won't detect if an object property or
 * a collection item changes. For that reason, `ngOptions` additionally watches the model using
 * `$watchCollection`, when the expression contains a `track by` clause or the the select has the `multiple` attribute.
 * This allows ngOptions to trigger a re-rendering of the options even if the actual object/collection
 * has not changed identity, but only a property on the object or an item in the collection changes.
 *
 * Note that `$watchCollection` does a shallow comparison of the properties of the object (or the items in the collection
 * if the model is an array). This means that changing a property deeper than the first level inside the
 * object/collection will not trigger a re-rendering.
 *
 * ## `select` **`as`**
 *
 * Using `select` **`as`** will bind the result of the `select` expression to the model, but
 * the value of the `<select>` and `<option>` html elements will be either the index (for array data sources)
 * or property name (for object data sources) of the value within the collection. If a **`track by`** expression
 * is used, the result of that expression will be set as the value of the `option` and `select` elements.
 *
 *
 * ### `select` **`as`** and **`track by`**
 *
 * <div class="alert alert-warning">
 * Be careful when using `select` **`as`** and **`track by`** in the same expression.
 * </div>
 *
 * Given this array of items on the $scope:
 *
 * ```js
 * $scope.items = [{
 *   id: 1,
 *   label: 'aLabel',
 *   subItem: { name: 'aSubItem' }
 * }, {
 *   id: 2,
 *   label: 'bLabel',
 *   subItem: { name: 'bSubItem' }
 * }];
 * ```
 *
 * This will work:
 *
 * ```html
 * <select ng-options="item as item.label for item in items track by item.id" ng-model="selected"></select>
 * ```
 * ```js
 * $scope.selected = $scope.items[0];
 * ```
 *
 * but this will not work:
 *
 * ```html
 * <select ng-options="item.subItem as item.label for item in items track by item.id" ng-model="selected"></select>
 * ```
 * ```js
 * $scope.selected = $scope.items[0].subItem;
 * ```
 *
 * In both examples, the **`track by`** expression is applied successfully to each `item` in the
 * `items` array. Because the selected option has been set programmatically in the controller, the
 * **`track by`** expression is also applied to the `ngModel` value. In the first example, the
 * `ngModel` value is `items[0]` and the **`track by`** expression evaluates to `items[0].id` with
 * no issue. In the second example, the `ngModel` value is `items[0].subItem` and the **`track by`**
 * expression evaluates to `items[0].subItem.id` (which is undefined). As a result, the model value
 * is not matched against any `<option>` and the `<select>` appears as having no selected value.
 *
 *
 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
 * @param {comprehension_expression} ngOptions in one of the following forms:
 *
 *   * for array data sources:
 *     * `label` **`for`** `value` **`in`** `array`
 *     * `select` **`as`** `label` **`for`** `value` **`in`** `array`
 *     * `label` **`group by`** `group` **`for`** `value` **`in`** `array`
 *     * `label` **`disable when`** `disable` **`for`** `value` **`in`** `array`
 *     * `label` **`group by`** `group` **`for`** `value` **`in`** `array` **`track by`** `trackexpr`
 *     * `label` **`disable when`** `disable` **`for`** `value` **`in`** `array` **`track by`** `trackexpr`
 *     * `label` **`for`** `value` **`in`** `array` | orderBy:`orderexpr` **`track by`** `trackexpr`
 *        (for including a filter with `track by`)
 *   * for object data sources:
 *     * `label` **`for (`**`key` **`,`** `value`**`) in`** `object`
 *     * `select` **`as`** `label` **`for (`**`key` **`,`** `value`**`) in`** `object`
 *     * `label` **`group by`** `group` **`for (`**`key`**`,`** `value`**`) in`** `object`
 *     * `label` **`disable when`** `disable` **`for (`**`key`**`,`** `value`**`) in`** `object`
 *     * `select` **`as`** `label` **`group by`** `group`
 *         **`for` `(`**`key`**`,`** `value`**`) in`** `object`
 *     * `select` **`as`** `label` **`disable when`** `disable`
 *         **`for` `(`**`key`**`,`** `value`**`) in`** `object`
 *
 * Where:
 *
 *   * `array` / `object`: an expression which evaluates to an array / object to iterate over.
 *   * `value`: local variable which will refer to each item in the `array` or each property value
 *      of `object` during iteration.
 *   * `key`: local variable which will refer to a property name in `object` during iteration.
 *   * `label`: The result of this expression will be the label for `<option>` element. The
 *     `expression` will most likely refer to the `value` variable (e.g. `value.propertyName`).
 *   * `select`: The result of this expression will be bound to the model of the parent `<select>`
 *      element. If not specified, `select` expression will default to `value`.
 *   * `group`: The result of this expression will be used to group options using the `<optgroup>`
 *      DOM element.
 *   * `disable`: The result of this expression will be used to disable the rendered `<option>`
 *      element. Return `true` to disable.
 *   * `trackexpr`: Used when working with an array of objects. The result of this expression will be
 *      used to identify the objects in the array. The `trackexpr` will most likely refer to the
 *     `value` variable (e.g. `value.propertyName`). With this the selection is preserved
 *      even when the options are recreated (e.g. reloaded from the server).
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} required The control is considered valid only if value is entered.
 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
 *    `required` when you want to data-bind to the `required` attribute.
 * @param {string=} ngAttrSize sets the size of the select element dynamically. Uses the
 * {@link guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes ngAttr} directive.
 *
 * @example
    <example module="selectExample" name="select">
      <file name="index.html">
        <script>
        angular.module('selectExample', [])
          .controller('ExampleController', ['$scope', function($scope) {
            $scope.colors = [
              {name:'black', shade:'dark'},
              {name:'white', shade:'light', notAnOption: true},
              {name:'red', shade:'dark'},
              {name:'blue', shade:'dark', notAnOption: true},
              {name:'yellow', shade:'light', notAnOption: false}
            ];
            $scope.myColor = $scope.colors[2]; // red
          }]);
        </script>
        <div ng-controller="ExampleController">
          <ul>
            <li ng-repeat="color in colors">
              <label>Name: <input ng-model="color.name"></label>
              <label><input type="checkbox" ng-model="color.notAnOption"> Disabled?</label>
              <button ng-click="colors.splice($index, 1)" aria-label="Remove">X</button>
            </li>
            <li>
              <button ng-click="colors.push({})">add</button>
            </li>
          </ul>
          <hr/>
          <label>Color (null not allowed):
            <select ng-model="myColor" ng-options="color.name for color in colors"></select>
          </label><br/>
          <label>Color (null allowed):
          <span  class="nullable">
            <select ng-model="myColor" ng-options="color.name for color in colors">
              <option value="">-- choose color --</option>
            </select>
          </span></label><br/>

          <label>Color grouped by shade:
            <select ng-model="myColor" ng-options="color.name group by color.shade for color in colors">
            </select>
          </label><br/>

          <label>Color grouped by shade, with some disabled:
            <select ng-model="myColor"
                  ng-options="color.name group by color.shade disable when color.notAnOption for color in colors">
            </select>
          </label><br/>



          Select <button ng-click="myColor = { name:'not in list', shade: 'other' }">bogus</button>.
          <br/>
          <hr/>
          Currently selected: {{ {selected_color:myColor} }}
          <div style="border:solid 1px black; height:20px"
               ng-style="{'background-color':myColor.name}">
          </div>
        </div>
      </file>
      <file name="protractor.js" type="protractor">
         it('should check ng-options', function() {
           expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('red');
           element.all(by.model('myColor')).first().click();
           element.all(by.css('select[ng-model="myColor"] option')).first().click();
           expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('black');
           element(by.css('.nullable select[ng-model="myColor"]')).click();
           element.all(by.css('.nullable select[ng-model="myColor"] option')).first().click();
           expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('null');
         });
      </file>
    </example>
 */

/* eslint-disable max-len */
//                     //00001111111111000000000002222222222000000000000000000000333333333300000000000000000000000004444444444400000000000005555555555555000000000666666666666600000007777777777777000000000000000888888888800000000000000000009999999999
var NG_OPTIONS_REGEXP = /^\s*([\s\S]+?)(?:\s+as\s+([\s\S]+?))?(?:\s+group\s+by\s+([\s\S]+?))?(?:\s+disable\s+when\s+([\s\S]+?))?\s+for\s+(?:([$\w][$\w]*)|(?:\(\s*([$\w][$\w]*)\s*,\s*([$\w][$\w]*)\s*\)))\s+in\s+([\s\S]+?)(?:\s+track\s+by\s+([\s\S]+?))?$/;
                        // 1: value expression (valueFn)
                        // 2: label expression (displayFn)
                        // 3: group by expression (groupByFn)
                        // 4: disable when expression (disableWhenFn)
                        // 5: array item variable name
                        // 6: object item key variable name
                        // 7: object item value variable name
                        // 8: collection expression
                        // 9: track by expression
/* eslint-enable */


var ngOptionsDirective = ['$compile', '$document', '$parse', function($compile, $document, $parse) {

  function parseOptionsExpression(optionsExp, selectElement, scope) {

    var match = optionsExp.match(NG_OPTIONS_REGEXP);
    if (!(match)) {
      throw ngOptionsMinErr('iexp',
        'Expected expression in form of ' +
        '\'_select_ (as _label_)? for (_key_,)?_value_ in _collection_\'' +
        ' but got \'{0}\'. Element: {1}',
        optionsExp, startingTag(selectElement));
    }

    // Extract the parts from the ngOptions expression

    // The variable name for the value of the item in the collection
    var valueName = match[5] || match[7];
    // The variable name for the key of the item in the collection
    var keyName = match[6];

    // An expression that generates the viewValue for an option if there is a label expression
    var selectAs = / as /.test(match[0]) && match[1];
    // An expression that is used to track the id of each object in the options collection
    var trackBy = match[9];
    // An expression that generates the viewValue for an option if there is no label expression
    var valueFn = $parse(match[2] ? match[1] : valueName);
    var selectAsFn = selectAs && $parse(selectAs);
    var viewValueFn = selectAsFn || valueFn;
    var trackByFn = trackBy && $parse(trackBy);

    // Get the value by which we are going to track the option
    // if we have a trackFn then use that (passing scope and locals)
    // otherwise just hash the given viewValue
    var getTrackByValueFn = trackBy ?
                              function(value, locals) { return trackByFn(scope, locals); } :
                              function getHashOfValue(value) { return hashKey(value); };
    var getTrackByValue = function(value, key) {
      return getTrackByValueFn(value, getLocals(value, key));
    };

    var displayFn = $parse(match[2] || match[1]);
    var groupByFn = $parse(match[3] || '');
    var disableWhenFn = $parse(match[4] || '');
    var valuesFn = $parse(match[8]);

    var locals = {};
    var getLocals = keyName ? function(value, key) {
      locals[keyName] = key;
      locals[valueName] = value;
      return locals;
    } : function(value) {
      locals[valueName] = value;
      return locals;
    };


    function Option(selectValue, viewValue, label, group, disabled) {
      this.selectValue = selectValue;
      this.viewValue = viewValue;
      this.label = label;
      this.group = group;
      this.disabled = disabled;
    }

    function getOptionValuesKeys(optionValues) {
      var optionValuesKeys;

      if (!keyName && isArrayLike(optionValues)) {
        optionValuesKeys = optionValues;
      } else {
        // if object, extract keys, in enumeration order, unsorted
        optionValuesKeys = [];
        for (var itemKey in optionValues) {
          if (optionValues.hasOwnProperty(itemKey) && itemKey.charAt(0) !== '$') {
            optionValuesKeys.push(itemKey);
          }
        }
      }
      return optionValuesKeys;
    }

    return {
      trackBy: trackBy,
      getTrackByValue: getTrackByValue,
      getWatchables: $parse(valuesFn, function(optionValues) {
        // Create a collection of things that we would like to watch (watchedArray)
        // so that they can all be watched using a single $watchCollection
        // that only runs the handler once if anything changes
        var watchedArray = [];
        optionValues = optionValues || [];

        var optionValuesKeys = getOptionValuesKeys(optionValues);
        var optionValuesLength = optionValuesKeys.length;
        for (var index = 0; index < optionValuesLength; index++) {
          var key = (optionValues === optionValuesKeys) ? index : optionValuesKeys[index];
          var value = optionValues[key];

          var locals = getLocals(value, key);
          var selectValue = getTrackByValueFn(value, locals);
          watchedArray.push(selectValue);

          // Only need to watch the displayFn if there is a specific label expression
          if (match[2] || match[1]) {
            var label = displayFn(scope, locals);
            watchedArray.push(label);
          }

          // Only need to watch the disableWhenFn if there is a specific disable expression
          if (match[4]) {
            var disableWhen = disableWhenFn(scope, locals);
            watchedArray.push(disableWhen);
          }
        }
        return watchedArray;
      }),

      getOptions: function() {

        var optionItems = [];
        var selectValueMap = {};

        // The option values were already computed in the `getWatchables` fn,
        // which must have been called to trigger `getOptions`
        var optionValues = valuesFn(scope) || [];
        var optionValuesKeys = getOptionValuesKeys(optionValues);
        var optionValuesLength = optionValuesKeys.length;

        for (var index = 0; index < optionValuesLength; index++) {
          var key = (optionValues === optionValuesKeys) ? index : optionValuesKeys[index];
          var value = optionValues[key];
          var locals = getLocals(value, key);
          var viewValue = viewValueFn(scope, locals);
          var selectValue = getTrackByValueFn(viewValue, locals);
          var label = displayFn(scope, locals);
          var group = groupByFn(scope, locals);
          var disabled = disableWhenFn(scope, locals);
          var optionItem = new Option(selectValue, viewValue, label, group, disabled);

          optionItems.push(optionItem);
          selectValueMap[selectValue] = optionItem;
        }

        return {
          items: optionItems,
          selectValueMap: selectValueMap,
          getOptionFromViewValue: function(value) {
            return selectValueMap[getTrackByValue(value)];
          },
          getViewValueFromOption: function(option) {
            // If the viewValue could be an object that may be mutated by the application,
            // we need to make a copy and not return the reference to the value on the option.
            return trackBy ? copy(option.viewValue) : option.viewValue;
          }
        };
      }
    };
  }


  // we can't just jqLite('<option>') since jqLite is not smart enough
  // to create it in <select> and IE barfs otherwise.
  var optionTemplate = window.document.createElement('option'),
      optGroupTemplate = window.document.createElement('optgroup');

    function ngOptionsPostLink(scope, selectElement, attr, ctrls) {

      var selectCtrl = ctrls[0];
      var ngModelCtrl = ctrls[1];
      var multiple = attr.multiple;

      // The emptyOption allows the application developer to provide their own custom "empty"
      // option when the viewValue does not match any of the option values.
      for (var i = 0, children = selectElement.children(), ii = children.length; i < ii; i++) {
        if (children[i].value === '') {
          selectCtrl.hasEmptyOption = true;
          selectCtrl.emptyOption = children.eq(i);
          break;
        }
      }

      var providedEmptyOption = !!selectCtrl.emptyOption;

      var unknownOption = jqLite(optionTemplate.cloneNode(false));
      unknownOption.val('?');

      var options;
      var ngOptions = parseOptionsExpression(attr.ngOptions, selectElement, scope);
      // This stores the newly created options before they are appended to the select.
      // Since the contents are removed from the fragment when it is appended,
      // we only need to create it once.
      var listFragment = $document[0].createDocumentFragment();

      // Overwrite the implementation. ngOptions doesn't use hashes
      selectCtrl.generateUnknownOptionValue = function(val) {
        return '?';
      };

      // Update the controller methods for multiple selectable options
      if (!multiple) {

        selectCtrl.writeValue = function writeNgOptionsValue(value) {
          var selectedOption = options.selectValueMap[selectElement.val()];
          var option = options.getOptionFromViewValue(value);

          // Make sure to remove the selected attribute from the previously selected option
          // Otherwise, screen readers might get confused
          if (selectedOption) selectedOption.element.removeAttribute('selected');

          if (option) {
            // Don't update the option when it is already selected.
            // For example, the browser will select the first option by default. In that case,
            // most properties are set automatically - except the `selected` attribute, which we
            // set always

            if (selectElement[0].value !== option.selectValue) {
              selectCtrl.removeUnknownOption();
              selectCtrl.unselectEmptyOption();

              selectElement[0].value = option.selectValue;
              option.element.selected = true;
            }

            option.element.setAttribute('selected', 'selected');
          } else {

            if (providedEmptyOption) {
              selectCtrl.selectEmptyOption();
            } else if (selectCtrl.unknownOption.parent().length) {
              selectCtrl.updateUnknownOption(value);
            } else {
              selectCtrl.renderUnknownOption(value);
            }
          }
        };

        selectCtrl.readValue = function readNgOptionsValue() {

          var selectedOption = options.selectValueMap[selectElement.val()];

          if (selectedOption && !selectedOption.disabled) {
            selectCtrl.unselectEmptyOption();
            selectCtrl.removeUnknownOption();
            return options.getViewValueFromOption(selectedOption);
          }
          return null;
        };

        // If we are using `track by` then we must watch the tracked value on the model
        // since ngModel only watches for object identity change
        // FIXME: When a user selects an option, this watch will fire needlessly
        if (ngOptions.trackBy) {
          scope.$watch(
            function() { return ngOptions.getTrackByValue(ngModelCtrl.$viewValue); },
            function() { ngModelCtrl.$render(); }
          );
        }

      } else {

        selectCtrl.writeValue = function writeNgOptionsMultiple(values) {
          // Only set `<option>.selected` if necessary, in order to prevent some browsers from
          // scrolling to `<option>` elements that are outside the `<select>` element's viewport.

          var selectedOptions = values && values.map(getAndUpdateSelectedOption) || [];

          options.items.forEach(function(option) {
            if (option.element.selected && !includes(selectedOptions, option)) {
              option.element.selected = false;
            }
          });
        };


        selectCtrl.readValue = function readNgOptionsMultiple() {
          var selectedValues = selectElement.val() || [],
              selections = [];

          forEach(selectedValues, function(value) {
            var option = options.selectValueMap[value];
            if (option && !option.disabled) selections.push(options.getViewValueFromOption(option));
          });

          return selections;
        };

        // If we are using `track by` then we must watch these tracked values on the model
        // since ngModel only watches for object identity change
        if (ngOptions.trackBy) {

          scope.$watchCollection(function() {
            if (isArray(ngModelCtrl.$viewValue)) {
              return ngModelCtrl.$viewValue.map(function(value) {
                return ngOptions.getTrackByValue(value);
              });
            }
          }, function() {
            ngModelCtrl.$render();
          });

        }
      }

      if (providedEmptyOption) {

        // we need to remove it before calling selectElement.empty() because otherwise IE will
        // remove the label from the element. wtf?
        selectCtrl.emptyOption.remove();

        // compile the element since there might be bindings in it
        $compile(selectCtrl.emptyOption)(scope);

        if (selectCtrl.emptyOption[0].nodeType === NODE_TYPE_COMMENT) {
          // This means the empty option has currently no actual DOM node, probably because
          // it has been modified by a transclusion directive.
          selectCtrl.hasEmptyOption = false;

          // Redefine the registerOption function, which will catch
          // options that are added by ngIf etc. (rendering of the node is async because of
          // lazy transclusion)
          selectCtrl.registerOption = function(optionScope, optionEl) {
            if (optionEl.val() === '') {
              selectCtrl.hasEmptyOption = true;
              selectCtrl.emptyOption = optionEl;
              selectCtrl.emptyOption.removeClass('ng-scope');
              // This ensures the new empty option is selected if previously no option was selected
              ngModelCtrl.$render();

              optionEl.on('$destroy', function() {
                selectCtrl.hasEmptyOption = false;
                selectCtrl.emptyOption = undefined;
              });
            }
          };

        } else {
          // remove the class, which is added automatically because we recompile the element and it
          // becomes the compilation root
          selectCtrl.emptyOption.removeClass('ng-scope');
        }

      }

      selectElement.empty();

      // We need to do this here to ensure that the options object is defined
      // when we first hit it in writeNgOptionsValue
      updateOptions();

      // We will re-render the option elements if the option values or labels change
      scope.$watchCollection(ngOptions.getWatchables, updateOptions);

      // ------------------------------------------------------------------ //

      function addOptionElement(option, parent) {
        var optionElement = optionTemplate.cloneNode(false);
        parent.appendChild(optionElement);
        updateOptionElement(option, optionElement);
      }

      function getAndUpdateSelectedOption(viewValue) {
        var option = options.getOptionFromViewValue(viewValue);
        var element = option && option.element;

        if (element && !element.selected) element.selected = true;

        return option;
      }

      function updateOptionElement(option, element) {
        option.element = element;
        element.disabled = option.disabled;
        // NOTE: The label must be set before the value, otherwise IE10/11/EDGE create unresponsive
        // selects in certain circumstances when multiple selects are next to each other and display
        // the option list in listbox style, i.e. the select is [multiple], or specifies a [size].
        // See https://github.com/angular/angular.js/issues/11314 for more info.
        // This is unfortunately untestable with unit / e2e tests
        if (option.label !== element.label) {
          element.label = option.label;
          element.textContent = option.label;
        }
        element.value = option.selectValue;
      }

      function updateOptions() {
        var previousValue = options && selectCtrl.readValue();

        // We must remove all current options, but cannot simply set innerHTML = null
        // since the providedEmptyOption might have an ngIf on it that inserts comments which we
        // must preserve.
        // Instead, iterate over the current option elements and remove them or their optgroup
        // parents
        if (options) {

          for (var i = options.items.length - 1; i >= 0; i--) {
            var option = options.items[i];
            if (isDefined(option.group)) {
              jqLiteRemove(option.element.parentNode);
            } else {
              jqLiteRemove(option.element);
            }
          }
        }

        options = ngOptions.getOptions();

        var groupElementMap = {};

        // Ensure that the empty option is always there if it was explicitly provided
        if (providedEmptyOption) {
          selectElement.prepend(selectCtrl.emptyOption);
        }

        options.items.forEach(function addOption(option) {
          var groupElement;

          if (isDefined(option.group)) {

            // This option is to live in a group
            // See if we have already created this group
            groupElement = groupElementMap[option.group];

            if (!groupElement) {

              groupElement = optGroupTemplate.cloneNode(false);
              listFragment.appendChild(groupElement);

              // Update the label on the group element
              // "null" is special cased because of Safari
              groupElement.label = option.group === null ? 'null' : option.group;

              // Store it for use later
              groupElementMap[option.group] = groupElement;
            }

            addOptionElement(option, groupElement);

          } else {

            // This option is not in a group
            addOptionElement(option, listFragment);
          }
        });

        selectElement[0].appendChild(listFragment);

        ngModelCtrl.$render();

        // Check to see if the value has changed due to the update to the options
        if (!ngModelCtrl.$isEmpty(previousValue)) {
          var nextValue = selectCtrl.readValue();
          var isNotPrimitive = ngOptions.trackBy || multiple;
          if (isNotPrimitive ? !equals(previousValue, nextValue) : previousValue !== nextValue) {
            ngModelCtrl.$setViewValue(nextValue);
            ngModelCtrl.$render();
          }
        }

      }
  }

  return {
    restrict: 'A',
    terminal: true,
    require: ['select', 'ngModel'],
    link: {
      pre: function ngOptionsPreLink(scope, selectElement, attr, ctrls) {
        // Deactivate the SelectController.register method to prevent
        // option directives from accidentally registering themselves
        // (and unwanted $destroy handlers etc.)
        ctrls[0].registerOption = noop;
      },
      post: ngOptionsPostLink
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngPluralize
 * @restrict EA
 *
 * @description
 * `ngPluralize` is a directive that displays messages according to en-US localization rules.
 * These rules are bundled with angular.js, but can be overridden
 * (see {@link guide/i18n Angular i18n} dev guide). You configure ngPluralize directive
 * by specifying the mappings between
 * [plural categories](http://unicode.org/repos/cldr-tmp/trunk/diff/supplemental/language_plural_rules.html)
 * and the strings to be displayed.
 *
 * # Plural categories and explicit number rules
 * There are two
 * [plural categories](http://unicode.org/repos/cldr-tmp/trunk/diff/supplemental/language_plural_rules.html)
 * in Angular's default en-US locale: "one" and "other".
 *
 * While a plural category may match many numbers (for example, in en-US locale, "other" can match
 * any number that is not 1), an explicit number rule can only match one number. For example, the
 * explicit number rule for "3" matches the number 3. There are examples of plural categories
 * and explicit number rules throughout the rest of this documentation.
 *
 * # Configuring ngPluralize
 * You configure ngPluralize by providing 2 attributes: `count` and `when`.
 * You can also provide an optional attribute, `offset`.
 *
 * The value of the `count` attribute can be either a string or an {@link guide/expression
 * Angular expression}; these are evaluated on the current scope for its bound value.
 *
 * The `when` attribute specifies the mappings between plural categories and the actual
 * string to be displayed. The value of the attribute should be a JSON object.
 *
 * The following example shows how to configure ngPluralize:
 *
 * ```html
 * <ng-pluralize count="personCount"
                 when="{'0': 'Nobody is viewing.',
 *                      'one': '1 person is viewing.',
 *                      'other': '{} people are viewing.'}">
 * </ng-pluralize>
 *```
 *
 * In the example, `"0: Nobody is viewing."` is an explicit number rule. If you did not
 * specify this rule, 0 would be matched to the "other" category and "0 people are viewing"
 * would be shown instead of "Nobody is viewing". You can specify an explicit number rule for
 * other numbers, for example 12, so that instead of showing "12 people are viewing", you can
 * show "a dozen people are viewing".
 *
 * You can use a set of closed braces (`{}`) as a placeholder for the number that you want substituted
 * into pluralized strings. In the previous example, Angular will replace `{}` with
 * <span ng-non-bindable>`{{personCount}}`</span>. The closed braces `{}` is a placeholder
 * for <span ng-non-bindable>{{numberExpression}}</span>.
 *
 * If no rule is defined for a category, then an empty string is displayed and a warning is generated.
 * Note that some locales define more categories than `one` and `other`. For example, fr-fr defines `few` and `many`.
 *
 * # Configuring ngPluralize with offset
 * The `offset` attribute allows further customization of pluralized text, which can result in
 * a better user experience. For example, instead of the message "4 people are viewing this document",
 * you might display "John, Kate and 2 others are viewing this document".
 * The offset attribute allows you to offset a number by any desired value.
 * Let's take a look at an example:
 *
 * ```html
 * <ng-pluralize count="personCount" offset=2
 *               when="{'0': 'Nobody is viewing.',
 *                      '1': '{{person1}} is viewing.',
 *                      '2': '{{person1}} and {{person2}} are viewing.',
 *                      'one': '{{person1}}, {{person2}} and one other person are viewing.',
 *                      'other': '{{person1}}, {{person2}} and {} other people are viewing.'}">
 * </ng-pluralize>
 * ```
 *
 * Notice that we are still using two plural categories(one, other), but we added
 * three explicit number rules 0, 1 and 2.
 * When one person, perhaps John, views the document, "John is viewing" will be shown.
 * When three people view the document, no explicit number rule is found, so
 * an offset of 2 is taken off 3, and Angular uses 1 to decide the plural category.
 * In this case, plural category 'one' is matched and "John, Mary and one other person are viewing"
 * is shown.
 *
 * Note that when you specify offsets, you must provide explicit number rules for
 * numbers from 0 up to and including the offset. If you use an offset of 3, for example,
 * you must provide explicit number rules for 0, 1, 2 and 3. You must also provide plural strings for
 * plural categories "one" and "other".
 *
 * @param {string|expression} count The variable to be bound to.
 * @param {string} when The mapping between plural category to its corresponding strings.
 * @param {number=} offset Offset to deduct from the total number.
 *
 * @example
    <example module="pluralizeExample" name="ng-pluralize">
      <file name="index.html">
        <script>
          angular.module('pluralizeExample', [])
            .controller('ExampleController', ['$scope', function($scope) {
              $scope.person1 = 'Igor';
              $scope.person2 = 'Misko';
              $scope.personCount = 1;
            }]);
        </script>
        <div ng-controller="ExampleController">
          <label>Person 1:<input type="text" ng-model="person1" value="Igor" /></label><br/>
          <label>Person 2:<input type="text" ng-model="person2" value="Misko" /></label><br/>
          <label>Number of People:<input type="text" ng-model="personCount" value="1" /></label><br/>

          <!--- Example with simple pluralization rules for en locale --->
          Without Offset:
          <ng-pluralize count="personCount"
                        when="{'0': 'Nobody is viewing.',
                               'one': '1 person is viewing.',
                               'other': '{} people are viewing.'}">
          </ng-pluralize><br>

          <!--- Example with offset --->
          With Offset(2):
          <ng-pluralize count="personCount" offset=2
                        when="{'0': 'Nobody is viewing.',
                               '1': '{{person1}} is viewing.',
                               '2': '{{person1}} and {{person2}} are viewing.',
                               'one': '{{person1}}, {{person2}} and one other person are viewing.',
                               'other': '{{person1}}, {{person2}} and {} other people are viewing.'}">
          </ng-pluralize>
        </div>
      </file>
      <file name="protractor.js" type="protractor">
        it('should show correct pluralized string', function() {
          var withoutOffset = element.all(by.css('ng-pluralize')).get(0);
          var withOffset = element.all(by.css('ng-pluralize')).get(1);
          var countInput = element(by.model('personCount'));

          expect(withoutOffset.getText()).toEqual('1 person is viewing.');
          expect(withOffset.getText()).toEqual('Igor is viewing.');

          countInput.clear();
          countInput.sendKeys('0');

          expect(withoutOffset.getText()).toEqual('Nobody is viewing.');
          expect(withOffset.getText()).toEqual('Nobody is viewing.');

          countInput.clear();
          countInput.sendKeys('2');

          expect(withoutOffset.getText()).toEqual('2 people are viewing.');
          expect(withOffset.getText()).toEqual('Igor and Misko are viewing.');

          countInput.clear();
          countInput.sendKeys('3');

          expect(withoutOffset.getText()).toEqual('3 people are viewing.');
          expect(withOffset.getText()).toEqual('Igor, Misko and one other person are viewing.');

          countInput.clear();
          countInput.sendKeys('4');

          expect(withoutOffset.getText()).toEqual('4 people are viewing.');
          expect(withOffset.getText()).toEqual('Igor, Misko and 2 other people are viewing.');
        });
        it('should show data-bound names', function() {
          var withOffset = element.all(by.css('ng-pluralize')).get(1);
          var personCount = element(by.model('personCount'));
          var person1 = element(by.model('person1'));
          var person2 = element(by.model('person2'));
          personCount.clear();
          personCount.sendKeys('4');
          person1.clear();
          person1.sendKeys('Di');
          person2.clear();
          person2.sendKeys('Vojta');
          expect(withOffset.getText()).toEqual('Di, Vojta and 2 other people are viewing.');
        });
      </file>
    </example>
 */
var ngPluralizeDirective = ['$locale', '$interpolate', '$log', function($locale, $interpolate, $log) {
  var BRACE = /{}/g,
      IS_WHEN = /^when(Minus)?(.+)$/;

  return {
    link: function(scope, element, attr) {
      var numberExp = attr.count,
          whenExp = attr.$attr.when && element.attr(attr.$attr.when), // we have {{}} in attrs
          offset = attr.offset || 0,
          whens = scope.$eval(whenExp) || {},
          whensExpFns = {},
          startSymbol = $interpolate.startSymbol(),
          endSymbol = $interpolate.endSymbol(),
          braceReplacement = startSymbol + numberExp + '-' + offset + endSymbol,
          watchRemover = angular.noop,
          lastCount;

      forEach(attr, function(expression, attributeName) {
        var tmpMatch = IS_WHEN.exec(attributeName);
        if (tmpMatch) {
          var whenKey = (tmpMatch[1] ? '-' : '') + lowercase(tmpMatch[2]);
          whens[whenKey] = element.attr(attr.$attr[attributeName]);
        }
      });
      forEach(whens, function(expression, key) {
        whensExpFns[key] = $interpolate(expression.replace(BRACE, braceReplacement));

      });

      scope.$watch(numberExp, function ngPluralizeWatchAction(newVal) {
        var count = parseFloat(newVal);
        var countIsNaN = isNumberNaN(count);

        if (!countIsNaN && !(count in whens)) {
          // If an explicit number rule such as 1, 2, 3... is defined, just use it.
          // Otherwise, check it against pluralization rules in $locale service.
          count = $locale.pluralCat(count - offset);
        }

        // If both `count` and `lastCount` are NaN, we don't need to re-register a watch.
        // In JS `NaN !== NaN`, so we have to explicitly check.
        if ((count !== lastCount) && !(countIsNaN && isNumberNaN(lastCount))) {
          watchRemover();
          var whenExpFn = whensExpFns[count];
          if (isUndefined(whenExpFn)) {
            if (newVal != null) {
              $log.debug('ngPluralize: no rule defined for \'' + count + '\' in ' + whenExp);
            }
            watchRemover = noop;
            updateElementText();
          } else {
            watchRemover = scope.$watch(whenExpFn, updateElementText);
          }
          lastCount = count;
        }
      });

      function updateElementText(newText) {
        element.text(newText || '');
      }
    }
  };
}];

/* exported ngRepeatDirective */

/**
 * @ngdoc directive
 * @name ngRepeat
 * @multiElement
 * @restrict A
 *
 * @description
 * The `ngRepeat` directive instantiates a template once per item from a collection. Each template
 * instance gets its own scope, where the given loop variable is set to the current collection item,
 * and `$index` is set to the item index or key.
 *
 * Special properties are exposed on the local scope of each template instance, including:
 *
 * | Variable  | Type            | Details                                                                     |
 * |-----------|-----------------|-----------------------------------------------------------------------------|
 * | `$index`  | {@type number}  | iterator offset of the repeated element (0..length-1)                       |
 * | `$first`  | {@type boolean} | true if the repeated element is first in the iterator.                      |
 * | `$middle` | {@type boolean} | true if the repeated element is between the first and last in the iterator. |
 * | `$last`   | {@type boolean} | true if the repeated element is last in the iterator.                       |
 * | `$even`   | {@type boolean} | true if the iterator position `$index` is even (otherwise false).           |
 * | `$odd`    | {@type boolean} | true if the iterator position `$index` is odd (otherwise false).            |
 *
 * <div class="alert alert-info">
 *   Creating aliases for these properties is possible with {@link ng.directive:ngInit `ngInit`}.
 *   This may be useful when, for instance, nesting ngRepeats.
 * </div>
 *
 *
 * # Iterating over object properties
 *
 * It is possible to get `ngRepeat` to iterate over the properties of an object using the following
 * syntax:
 *
 * ```js
 * <div ng-repeat="(key, value) in myObj"> ... </div>
 * ```
 *
 * However, there are a few limitations compared to array iteration:
 *
 * - The JavaScript specification does not define the order of keys
 *   returned for an object, so Angular relies on the order returned by the browser
 *   when running `for key in myObj`. Browsers generally follow the strategy of providing
 *   keys in the order in which they were defined, although there are exceptions when keys are deleted
 *   and reinstated. See the
 *   [MDN page on `delete` for more info](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/delete#Cross-browser_notes).
 *
 * - `ngRepeat` will silently *ignore* object keys starting with `$`, because
 *   it's a prefix used by Angular for public (`$`) and private (`$$`) properties.
 *
 * - The built-in filters {@link ng.orderBy orderBy} and {@link ng.filter filter} do not work with
 *   objects, and will throw an error if used with one.
 *
 * If you are hitting any of these limitations, the recommended workaround is to convert your object into an array
 * that is sorted into the order that you prefer before providing it to `ngRepeat`. You could
 * do this with a filter such as [toArrayFilter](http://ngmodules.org/modules/angular-toArrayFilter)
 * or implement a `$watch` on the object yourself.
 *
 *
 * # Tracking and Duplicates
 *
 * `ngRepeat` uses {@link $rootScope.Scope#$watchCollection $watchCollection} to detect changes in
 * the collection. When a change happens, `ngRepeat` then makes the corresponding changes to the DOM:
 *
 * * When an item is added, a new instance of the template is added to the DOM.
 * * When an item is removed, its template instance is removed from the DOM.
 * * When items are reordered, their respective templates are reordered in the DOM.
 *
 * To minimize creation of DOM elements, `ngRepeat` uses a function
 * to "keep track" of all items in the collection and their corresponding DOM elements.
 * For example, if an item is added to the collection, `ngRepeat` will know that all other items
 * already have DOM elements, and will not re-render them.
 *
 * The default tracking function (which tracks items by their identity) does not allow
 * duplicate items in arrays. This is because when there are duplicates, it is not possible
 * to maintain a one-to-one mapping between collection items and DOM elements.
 *
 * If you do need to repeat duplicate items, you can substitute the default tracking behavior
 * with your own using the `track by` expression.
 *
 * For example, you may track items by the index of each item in the collection, using the
 * special scope property `$index`:
 * ```html
 *    <div ng-repeat="n in [42, 42, 43, 43] track by $index">
 *      {{n}}
 *    </div>
 * ```
 *
 * You may also use arbitrary expressions in `track by`, including references to custom functions
 * on the scope:
 * ```html
 *    <div ng-repeat="n in [42, 42, 43, 43] track by myTrackingFunction(n)">
 *      {{n}}
 *    </div>
 * ```
 *
 * <div class="alert alert-success">
 * If you are working with objects that have a unique identifier property, you should track
 * by this identifier instead of the object instance. Should you reload your data later, `ngRepeat`
 * will not have to rebuild the DOM elements for items it has already rendered, even if the
 * JavaScript objects in the collection have been substituted for new ones. For large collections,
 * this significantly improves rendering performance. If you don't have a unique identifier,
 * `track by $index` can also provide a performance boost.
 * </div>
 *
 * ```html
 *    <div ng-repeat="model in collection track by model.id">
 *      {{model.name}}
 *    </div>
 * ```
 *
 * <br />
 * <div class="alert alert-warning">
 * Avoid using `track by $index` when the repeated template contains
 * {@link guide/expression#one-time-binding one-time bindings}. In such cases, the `nth` DOM
 * element will always be matched with the `nth` item of the array, so the bindings on that element
 * will not be updated even when the corresponding item changes, essentially causing the view to get
 * out-of-sync with the underlying data.
 * </div>
 *
 * When no `track by` expression is provided, it is equivalent to tracking by the built-in
 * `$id` function, which tracks items by their identity:
 * ```html
 *    <div ng-repeat="obj in collection track by $id(obj)">
 *      {{obj.prop}}
 *    </div>
 * ```
 *
 * <br />
 * <div class="alert alert-warning">
 * **Note:** `track by` must always be the last expression:
 * </div>
 * ```
 *    <div ng-repeat="model in collection | orderBy: 'id' as filtered_result track by model.id">
 *      {{model.name}}
 *    </div>
 * ```
 *
 *
 * # Special repeat start and end points
 * To repeat a series of elements instead of just one parent element, ngRepeat (as well as other ng directives) supports extending
 * the range of the repeater by defining explicit start and end points by using **ng-repeat-start** and **ng-repeat-end** respectively.
 * The **ng-repeat-start** directive works the same as **ng-repeat**, but will repeat all the HTML code (including the tag it's defined on)
 * up to and including the ending HTML tag where **ng-repeat-end** is placed.
 *
 * The example below makes use of this feature:
 * ```html
 *   <header ng-repeat-start="item in items">
 *     Header {{ item }}
 *   </header>
 *   <div class="body">
 *     Body {{ item }}
 *   </div>
 *   <footer ng-repeat-end>
 *     Footer {{ item }}
 *   </footer>
 * ```
 *
 * And with an input of {@type ['A','B']} for the items variable in the example above, the output will evaluate to:
 * ```html
 *   <header>
 *     Header A
 *   </header>
 *   <div class="body">
 *     Body A
 *   </div>
 *   <footer>
 *     Footer A
 *   </footer>
 *   <header>
 *     Header B
 *   </header>
 *   <div class="body">
 *     Body B
 *   </div>
 *   <footer>
 *     Footer B
 *   </footer>
 * ```
 *
 * The custom start and end points for ngRepeat also support all other HTML directive syntax flavors provided in AngularJS (such
 * as **data-ng-repeat-start**, **x-ng-repeat-start** and **ng:repeat-start**).
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter} | when a new item is added to the list or when an item is revealed after a filter |
 * | {@link ng.$animate#leave leave} | when an item is removed from the list or when an item is filtered out |
 * | {@link ng.$animate#move move } | when an adjacent item is filtered out causing a reorder or when the item contents are reordered |
 *
 * See the example below for defining CSS animations with ngRepeat.
 *
 * @element ANY
 * @scope
 * @priority 1000
 * @param {repeat_expression} ngRepeat The expression indicating how to enumerate a collection. These
 *   formats are currently supported:
 *
 *   * `variable in expression` – where variable is the user defined loop variable and `expression`
 *     is a scope expression giving the collection to enumerate.
 *
 *     For example: `album in artist.albums`.
 *
 *   * `(key, value) in expression` – where `key` and `value` can be any user defined identifiers,
 *     and `expression` is the scope expression giving the collection to enumerate.
 *
 *     For example: `(name, age) in {'adam':10, 'amalie':12}`.
 *
 *   * `variable in expression track by tracking_expression` – You can also provide an optional tracking expression
 *     which can be used to associate the objects in the collection with the DOM elements. If no tracking expression
 *     is specified, ng-repeat associates elements by identity. It is an error to have
 *     more than one tracking expression value resolve to the same key. (This would mean that two distinct objects are
 *     mapped to the same DOM element, which is not possible.)
 *
 *     Note that the tracking expression must come last, after any filters, and the alias expression.
 *
 *     For example: `item in items` is equivalent to `item in items track by $id(item)`. This implies that the DOM elements
 *     will be associated by item identity in the array.
 *
 *     For example: `item in items track by $id(item)`. A built in `$id()` function can be used to assign a unique
 *     `$$hashKey` property to each item in the array. This property is then used as a key to associated DOM elements
 *     with the corresponding item in the array by identity. Moving the same object in array would move the DOM
 *     element in the same way in the DOM.
 *
 *     For example: `item in items track by item.id` is a typical pattern when the items come from the database. In this
 *     case the object identity does not matter. Two objects are considered equivalent as long as their `id`
 *     property is same.
 *
 *     For example: `item in items | filter:searchText track by item.id` is a pattern that might be used to apply a filter
 *     to items in conjunction with a tracking expression.
 *
 *   * `variable in expression as alias_expression` – You can also provide an optional alias expression which will then store the
 *     intermediate results of the repeater after the filters have been applied. Typically this is used to render a special message
 *     when a filter is active on the repeater, but the filtered result set is empty.
 *
 *     For example: `item in items | filter:x as results` will store the fragment of the repeated items as `results`, but only after
 *     the items have been processed through the filter.
 *
 *     Please note that `as [variable name] is not an operator but rather a part of ngRepeat micro-syntax so it can be used only at the end
 *     (and not as operator, inside an expression).
 *
 *     For example: `item in items | filter : x | orderBy : order | limitTo : limit as results` .
 *
 * @example
 * This example uses `ngRepeat` to display a list of people. A filter is used to restrict the displayed
 * results by name or by age. New (entering) and removed (leaving) items are animated.
  <example module="ngRepeat" name="ngRepeat" deps="angular-animate.js" animations="true" name="ng-repeat">
    <file name="index.html">
      <div ng-controller="repeatController">
        I have {{friends.length}} friends. They are:
        <input type="search" ng-model="q" placeholder="filter friends..." aria-label="filter friends" />
        <ul class="example-animate-container">
          <li class="animate-repeat" ng-repeat="friend in friends | filter:q as results">
            [{{$index + 1}}] {{friend.name}} who is {{friend.age}} years old.
          </li>
          <li class="animate-repeat" ng-if="results.length === 0">
            <strong>No results found...</strong>
          </li>
        </ul>
      </div>
    </file>
    <file name="script.js">
      angular.module('ngRepeat', ['ngAnimate']).controller('repeatController', function($scope) {
        $scope.friends = [
          {name:'John', age:25, gender:'boy'},
          {name:'Jessie', age:30, gender:'girl'},
          {name:'Johanna', age:28, gender:'girl'},
          {name:'Joy', age:15, gender:'girl'},
          {name:'Mary', age:28, gender:'girl'},
          {name:'Peter', age:95, gender:'boy'},
          {name:'Sebastian', age:50, gender:'boy'},
          {name:'Erika', age:27, gender:'girl'},
          {name:'Patrick', age:40, gender:'boy'},
          {name:'Samantha', age:60, gender:'girl'}
        ];
      });
    </file>
    <file name="animations.css">
      .example-animate-container {
        background:white;
        border:1px solid black;
        list-style:none;
        margin:0;
        padding:0 10px;
      }

      .animate-repeat {
        line-height:30px;
        list-style:none;
        box-sizing:border-box;
      }

      .animate-repeat.ng-move,
      .animate-repeat.ng-enter,
      .animate-repeat.ng-leave {
        transition:all linear 0.5s;
      }

      .animate-repeat.ng-leave.ng-leave-active,
      .animate-repeat.ng-move,
      .animate-repeat.ng-enter {
        opacity:0;
        max-height:0;
      }

      .animate-repeat.ng-leave,
      .animate-repeat.ng-move.ng-move-active,
      .animate-repeat.ng-enter.ng-enter-active {
        opacity:1;
        max-height:30px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      var friends = element.all(by.repeater('friend in friends'));

      it('should render initial data set', function() {
        expect(friends.count()).toBe(10);
        expect(friends.get(0).getText()).toEqual('[1] John who is 25 years old.');
        expect(friends.get(1).getText()).toEqual('[2] Jessie who is 30 years old.');
        expect(friends.last().getText()).toEqual('[10] Samantha who is 60 years old.');
        expect(element(by.binding('friends.length')).getText())
            .toMatch("I have 10 friends. They are:");
      });

       it('should update repeater when filter predicate changes', function() {
         expect(friends.count()).toBe(10);

         element(by.model('q')).sendKeys('ma');

         expect(friends.count()).toBe(2);
         expect(friends.get(0).getText()).toEqual('[1] Mary who is 28 years old.');
         expect(friends.last().getText()).toEqual('[2] Samantha who is 60 years old.');
       });
      </file>
    </example>
 */
var ngRepeatDirective = ['$parse', '$animate', '$compile', function($parse, $animate, $compile) {
  var NG_REMOVED = '$$NG_REMOVED';
  var ngRepeatMinErr = minErr('ngRepeat');

  var updateScope = function(scope, index, valueIdentifier, value, keyIdentifier, key, arrayLength) {
    // TODO(perf): generate setters to shave off ~40ms or 1-1.5%
    scope[valueIdentifier] = value;
    if (keyIdentifier) scope[keyIdentifier] = key;
    scope.$index = index;
    scope.$first = (index === 0);
    scope.$last = (index === (arrayLength - 1));
    scope.$middle = !(scope.$first || scope.$last);
    // eslint-disable-next-line no-bitwise
    scope.$odd = !(scope.$even = (index & 1) === 0);
  };

  var getBlockStart = function(block) {
    return block.clone[0];
  };

  var getBlockEnd = function(block) {
    return block.clone[block.clone.length - 1];
  };


  return {
    restrict: 'A',
    multiElement: true,
    transclude: 'element',
    priority: 1000,
    terminal: true,
    $$tlb: true,
    compile: function ngRepeatCompile($element, $attr) {
      var expression = $attr.ngRepeat;
      var ngRepeatEndComment = $compile.$$createComment('end ngRepeat', expression);

      var match = expression.match(/^\s*([\s\S]+?)\s+in\s+([\s\S]+?)(?:\s+as\s+([\s\S]+?))?(?:\s+track\s+by\s+([\s\S]+?))?\s*$/);

      if (!match) {
        throw ngRepeatMinErr('iexp', 'Expected expression in form of \'_item_ in _collection_[ track by _id_]\' but got \'{0}\'.',
            expression);
      }

      var lhs = match[1];
      var rhs = match[2];
      var aliasAs = match[3];
      var trackByExp = match[4];

      match = lhs.match(/^(?:(\s*[$\w]+)|\(\s*([$\w]+)\s*,\s*([$\w]+)\s*\))$/);

      if (!match) {
        throw ngRepeatMinErr('iidexp', '\'_item_\' in \'_item_ in _collection_\' should be an identifier or \'(_key_, _value_)\' expression, but got \'{0}\'.',
            lhs);
      }
      var valueIdentifier = match[3] || match[1];
      var keyIdentifier = match[2];

      if (aliasAs && (!/^[$a-zA-Z_][$a-zA-Z0-9_]*$/.test(aliasAs) ||
          /^(null|undefined|this|\$index|\$first|\$middle|\$last|\$even|\$odd|\$parent|\$root|\$id)$/.test(aliasAs))) {
        throw ngRepeatMinErr('badident', 'alias \'{0}\' is invalid --- must be a valid JS identifier which is not a reserved name.',
          aliasAs);
      }

      var trackByExpGetter, trackByIdExpFn, trackByIdArrayFn, trackByIdObjFn;
      var hashFnLocals = {$id: hashKey};

      if (trackByExp) {
        trackByExpGetter = $parse(trackByExp);
      } else {
        trackByIdArrayFn = function(key, value) {
          return hashKey(value);
        };
        trackByIdObjFn = function(key) {
          return key;
        };
      }

      return function ngRepeatLink($scope, $element, $attr, ctrl, $transclude) {

        if (trackByExpGetter) {
          trackByIdExpFn = function(key, value, index) {
            // assign key, value, and $index to the locals so that they can be used in hash functions
            if (keyIdentifier) hashFnLocals[keyIdentifier] = key;
            hashFnLocals[valueIdentifier] = value;
            hashFnLocals.$index = index;
            return trackByExpGetter($scope, hashFnLocals);
          };
        }

        // Store a list of elements from previous run. This is a hash where key is the item from the
        // iterator, and the value is objects with following properties.
        //   - scope: bound scope
        //   - element: previous element.
        //   - index: position
        //
        // We are using no-proto object so that we don't need to guard against inherited props via
        // hasOwnProperty.
        var lastBlockMap = createMap();

        //watch props
        $scope.$watchCollection(rhs, function ngRepeatAction(collection) {
          var index, length,
              previousNode = $element[0],     // node that cloned nodes should be inserted after
                                              // initialized to the comment node anchor
              nextNode,
              // Same as lastBlockMap but it has the current state. It will become the
              // lastBlockMap on the next iteration.
              nextBlockMap = createMap(),
              collectionLength,
              key, value, // key/value of iteration
              trackById,
              trackByIdFn,
              collectionKeys,
              block,       // last object information {scope, element, id}
              nextBlockOrder,
              elementsToRemove;

          if (aliasAs) {
            $scope[aliasAs] = collection;
          }

          if (isArrayLike(collection)) {
            collectionKeys = collection;
            trackByIdFn = trackByIdExpFn || trackByIdArrayFn;
          } else {
            trackByIdFn = trackByIdExpFn || trackByIdObjFn;
            // if object, extract keys, in enumeration order, unsorted
            collectionKeys = [];
            for (var itemKey in collection) {
              if (hasOwnProperty.call(collection, itemKey) && itemKey.charAt(0) !== '$') {
                collectionKeys.push(itemKey);
              }
            }
          }

          collectionLength = collectionKeys.length;
          nextBlockOrder = new Array(collectionLength);

          // locate existing items
          for (index = 0; index < collectionLength; index++) {
            key = (collection === collectionKeys) ? index : collectionKeys[index];
            value = collection[key];
            trackById = trackByIdFn(key, value, index);
            if (lastBlockMap[trackById]) {
              // found previously seen block
              block = lastBlockMap[trackById];
              delete lastBlockMap[trackById];
              nextBlockMap[trackById] = block;
              nextBlockOrder[index] = block;
            } else if (nextBlockMap[trackById]) {
              // if collision detected. restore lastBlockMap and throw an error
              forEach(nextBlockOrder, function(block) {
                if (block && block.scope) lastBlockMap[block.id] = block;
              });
              throw ngRepeatMinErr('dupes',
                  'Duplicates in a repeater are not allowed. Use \'track by\' expression to specify unique keys. Repeater: {0}, Duplicate key: {1}, Duplicate value: {2}',
                  expression, trackById, value);
            } else {
              // new never before seen block
              nextBlockOrder[index] = {id: trackById, scope: undefined, clone: undefined};
              nextBlockMap[trackById] = true;
            }
          }

          // remove leftover items
          for (var blockKey in lastBlockMap) {
            block = lastBlockMap[blockKey];
            elementsToRemove = getBlockNodes(block.clone);
            $animate.leave(elementsToRemove);
            if (elementsToRemove[0].parentNode) {
              // if the element was not removed yet because of pending animation, mark it as deleted
              // so that we can ignore it later
              for (index = 0, length = elementsToRemove.length; index < length; index++) {
                elementsToRemove[index][NG_REMOVED] = true;
              }
            }
            block.scope.$destroy();
          }

          // we are not using forEach for perf reasons (trying to avoid #call)
          for (index = 0; index < collectionLength; index++) {
            key = (collection === collectionKeys) ? index : collectionKeys[index];
            value = collection[key];
            block = nextBlockOrder[index];

            if (block.scope) {
              // if we have already seen this object, then we need to reuse the
              // associated scope/element

              nextNode = previousNode;

              // skip nodes that are already pending removal via leave animation
              do {
                nextNode = nextNode.nextSibling;
              } while (nextNode && nextNode[NG_REMOVED]);

              if (getBlockStart(block) !== nextNode) {
                // existing item which got moved
                $animate.move(getBlockNodes(block.clone), null, previousNode);
              }
              previousNode = getBlockEnd(block);
              updateScope(block.scope, index, valueIdentifier, value, keyIdentifier, key, collectionLength);
            } else {
              // new item which we don't know about
              $transclude(function ngRepeatTransclude(clone, scope) {
                block.scope = scope;
                // http://jsperf.com/clone-vs-createcomment
                var endNode = ngRepeatEndComment.cloneNode(false);
                clone[clone.length++] = endNode;

                $animate.enter(clone, null, previousNode);
                previousNode = endNode;
                // Note: We only need the first/last node of the cloned nodes.
                // However, we need to keep the reference to the jqlite wrapper as it might be changed later
                // by a directive with templateUrl when its template arrives.
                block.clone = clone;
                nextBlockMap[block.id] = block;
                updateScope(block.scope, index, valueIdentifier, value, keyIdentifier, key, collectionLength);
              });
            }
          }
          lastBlockMap = nextBlockMap;
        });
      };
    }
  };
}];

var NG_HIDE_CLASS = 'ng-hide';
var NG_HIDE_IN_PROGRESS_CLASS = 'ng-hide-animate';
/**
 * @ngdoc directive
 * @name ngShow
 * @multiElement
 *
 * @description
 * The `ngShow` directive shows or hides the given HTML element based on the expression provided to
 * the `ngShow` attribute.
 *
 * The element is shown or hidden by removing or adding the `.ng-hide` CSS class onto the element.
 * The `.ng-hide` CSS class is predefined in AngularJS and sets the display style to none (using an
 * `!important` flag). For CSP mode please add `angular-csp.css` to your HTML file (see
 * {@link ng.directive:ngCsp ngCsp}).
 *
 * ```html
 * <!-- when $scope.myValue is truthy (element is visible) -->
 * <div ng-show="myValue"></div>
 *
 * <!-- when $scope.myValue is falsy (element is hidden) -->
 * <div ng-show="myValue" class="ng-hide"></div>
 * ```
 *
 * When the `ngShow` expression evaluates to a falsy value then the `.ng-hide` CSS class is added
 * to the class attribute on the element causing it to become hidden. When truthy, the `.ng-hide`
 * CSS class is removed from the element causing the element not to appear hidden.
 *
 * ## Why is `!important` used?
 *
 * You may be wondering why `!important` is used for the `.ng-hide` CSS class. This is because the
 * `.ng-hide` selector can be easily overridden by heavier selectors. For example, something as
 * simple as changing the display style on a HTML list item would make hidden elements appear
 * visible. This also becomes a bigger issue when dealing with CSS frameworks.
 *
 * By using `!important`, the show and hide behavior will work as expected despite any clash between
 * CSS selector specificity (when `!important` isn't used with any conflicting styles). If a
 * developer chooses to override the styling to change how to hide an element then it is just a
 * matter of using `!important` in their own CSS code.
 *
 * ### Overriding `.ng-hide`
 *
 * By default, the `.ng-hide` class will style the element with `display: none !important`. If you
 * wish to change the hide behavior with `ngShow`/`ngHide`, you can simply overwrite the styles for
 * the `.ng-hide` CSS class. Note that the selector that needs to be used is actually
 * `.ng-hide:not(.ng-hide-animate)` to cope with extra animation classes that can be added.
 *
 * ```css
 * .ng-hide:not(.ng-hide-animate) {
 *   /&#42; These are just alternative ways of hiding an element &#42;/
 *   display: block!important;
 *   position: absolute;
 *   top: -9999px;
 *   left: -9999px;
 * }
 * ```
 *
 * By default you don't need to override anything in CSS and the animations will work around the
 * display style.
 *
 * ## A note about animations with `ngShow`
 *
 * Animations in `ngShow`/`ngHide` work with the show and hide events that are triggered when the
 * directive expression is true and false. This system works like the animation system present with
 * `ngClass` except that you must also include the `!important` flag to override the display
 * property so that the elements are not actually hidden during the animation.
 *
 * ```css
 * /&#42; A working example can be found at the bottom of this page. &#42;/
 * .my-element.ng-hide-add, .my-element.ng-hide-remove {
 *   transition: all 0.5s linear;
 * }
 *
 * .my-element.ng-hide-add { ... }
 * .my-element.ng-hide-add.ng-hide-add-active { ... }
 * .my-element.ng-hide-remove { ... }
 * .my-element.ng-hide-remove.ng-hide-remove-active { ... }
 * ```
 *
 * Keep in mind that, as of AngularJS version 1.3, there is no need to change the display property
 * to block during animation states - ngAnimate will automatically handle the style toggling for you.
 *
 * @animations
 * | Animation                                           | Occurs                                                                                                        |
 * |-----------------------------------------------------|---------------------------------------------------------------------------------------------------------------|
 * | {@link $animate#addClass addClass} `.ng-hide`       | After the `ngShow` expression evaluates to a non truthy value and just before the contents are set to hidden. |
 * | {@link $animate#removeClass removeClass} `.ng-hide` | After the `ngShow` expression evaluates to a truthy value and just before contents are set to visible.        |
 *
 * @element ANY
 * @param {expression} ngShow If the {@link guide/expression expression} is truthy/falsy then the
 *                            element is shown/hidden respectively.
 *
 * @example
 * A simple example, animating the element's opacity:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-show-simple">
    <file name="index.html">
      Show: <input type="checkbox" ng-model="checked" aria-label="Toggle ngShow"><br />
      <div class="check-element animate-show-hide" ng-show="checked">
        I show up when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      .animate-show-hide.ng-hide {
        opacity: 0;
      }

      .animate-show-hide.ng-hide-add,
      .animate-show-hide.ng-hide-remove {
        transition: all linear 0.5s;
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngShow', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(false);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(true);
      });
    </file>
  </example>
 *
 * <hr />
 * @example
 * A more complex example, featuring different show/hide animations:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-show-complex">
    <file name="index.html">
      Show: <input type="checkbox" ng-model="checked" aria-label="Toggle ngShow"><br />
      <div class="check-element funky-show-hide" ng-show="checked">
        I show up when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      body {
        overflow: hidden;
        perspective: 1000px;
      }

      .funky-show-hide.ng-hide-add {
        transform: rotateZ(0);
        transform-origin: right;
        transition: all 0.5s ease-in-out;
      }

      .funky-show-hide.ng-hide-add.ng-hide-add-active {
        transform: rotateZ(-135deg);
      }

      .funky-show-hide.ng-hide-remove {
        transform: rotateY(90deg);
        transform-origin: left;
        transition: all 0.5s ease;
      }

      .funky-show-hide.ng-hide-remove.ng-hide-remove-active {
        transform: rotateY(0);
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngShow', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(false);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(true);
      });
    </file>
  </example>
 */
var ngShowDirective = ['$animate', function($animate) {
  return {
    restrict: 'A',
    multiElement: true,
    link: function(scope, element, attr) {
      scope.$watch(attr.ngShow, function ngShowWatchAction(value) {
        // we're adding a temporary, animation-specific class for ng-hide since this way
        // we can control when the element is actually displayed on screen without having
        // to have a global/greedy CSS selector that breaks when other animations are run.
        // Read: https://github.com/angular/angular.js/issues/9103#issuecomment-58335845
        $animate[value ? 'removeClass' : 'addClass'](element, NG_HIDE_CLASS, {
          tempClasses: NG_HIDE_IN_PROGRESS_CLASS
        });
      });
    }
  };
}];


/**
 * @ngdoc directive
 * @name ngHide
 * @multiElement
 *
 * @description
 * The `ngHide` directive shows or hides the given HTML element based on the expression provided to
 * the `ngHide` attribute.
 *
 * The element is shown or hidden by removing or adding the `.ng-hide` CSS class onto the element.
 * The `.ng-hide` CSS class is predefined in AngularJS and sets the display style to none (using an
 * `!important` flag). For CSP mode please add `angular-csp.css` to your HTML file (see
 * {@link ng.directive:ngCsp ngCsp}).
 *
 * ```html
 * <!-- when $scope.myValue is truthy (element is hidden) -->
 * <div ng-hide="myValue" class="ng-hide"></div>
 *
 * <!-- when $scope.myValue is falsy (element is visible) -->
 * <div ng-hide="myValue"></div>
 * ```
 *
 * When the `ngHide` expression evaluates to a truthy value then the `.ng-hide` CSS class is added
 * to the class attribute on the element causing it to become hidden. When falsy, the `.ng-hide`
 * CSS class is removed from the element causing the element not to appear hidden.
 *
 * ## Why is `!important` used?
 *
 * You may be wondering why `!important` is used for the `.ng-hide` CSS class. This is because the
 * `.ng-hide` selector can be easily overridden by heavier selectors. For example, something as
 * simple as changing the display style on a HTML list item would make hidden elements appear
 * visible. This also becomes a bigger issue when dealing with CSS frameworks.
 *
 * By using `!important`, the show and hide behavior will work as expected despite any clash between
 * CSS selector specificity (when `!important` isn't used with any conflicting styles). If a
 * developer chooses to override the styling to change how to hide an element then it is just a
 * matter of using `!important` in their own CSS code.
 *
 * ### Overriding `.ng-hide`
 *
 * By default, the `.ng-hide` class will style the element with `display: none !important`. If you
 * wish to change the hide behavior with `ngShow`/`ngHide`, you can simply overwrite the styles for
 * the `.ng-hide` CSS class. Note that the selector that needs to be used is actually
 * `.ng-hide:not(.ng-hide-animate)` to cope with extra animation classes that can be added.
 *
 * ```css
 * .ng-hide:not(.ng-hide-animate) {
 *   /&#42; These are just alternative ways of hiding an element &#42;/
 *   display: block!important;
 *   position: absolute;
 *   top: -9999px;
 *   left: -9999px;
 * }
 * ```
 *
 * By default you don't need to override in CSS anything and the animations will work around the
 * display style.
 *
 * ## A note about animations with `ngHide`
 *
 * Animations in `ngShow`/`ngHide` work with the show and hide events that are triggered when the
 * directive expression is true and false. This system works like the animation system present with
 * `ngClass` except that you must also include the `!important` flag to override the display
 * property so that the elements are not actually hidden during the animation.
 *
 * ```css
 * /&#42; A working example can be found at the bottom of this page. &#42;/
 * .my-element.ng-hide-add, .my-element.ng-hide-remove {
 *   transition: all 0.5s linear;
 * }
 *
 * .my-element.ng-hide-add { ... }
 * .my-element.ng-hide-add.ng-hide-add-active { ... }
 * .my-element.ng-hide-remove { ... }
 * .my-element.ng-hide-remove.ng-hide-remove-active { ... }
 * ```
 *
 * Keep in mind that, as of AngularJS version 1.3, there is no need to change the display property
 * to block during animation states - ngAnimate will automatically handle the style toggling for you.
 *
 * @animations
 * | Animation                                           | Occurs                                                                                                     |
 * |-----------------------------------------------------|------------------------------------------------------------------------------------------------------------|
 * | {@link $animate#addClass addClass} `.ng-hide`       | After the `ngHide` expression evaluates to a truthy value and just before the contents are set to hidden.  |
 * | {@link $animate#removeClass removeClass} `.ng-hide` | After the `ngHide` expression evaluates to a non truthy value and just before contents are set to visible. |
 *
 *
 * @element ANY
 * @param {expression} ngHide If the {@link guide/expression expression} is truthy/falsy then the
 *                            element is hidden/shown respectively.
 *
 * @example
 * A simple example, animating the element's opacity:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-hide-simple">
    <file name="index.html">
      Hide: <input type="checkbox" ng-model="checked" aria-label="Toggle ngHide"><br />
      <div class="check-element animate-show-hide" ng-hide="checked">
        I hide when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      .animate-show-hide.ng-hide {
        opacity: 0;
      }

      .animate-show-hide.ng-hide-add,
      .animate-show-hide.ng-hide-remove {
        transition: all linear 0.5s;
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngHide', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(true);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(false);
      });
    </file>
  </example>
 *
 * <hr />
 * @example
 * A more complex example, featuring different show/hide animations:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-hide-complex">
    <file name="index.html">
      Hide: <input type="checkbox" ng-model="checked" aria-label="Toggle ngHide"><br />
      <div class="check-element funky-show-hide" ng-hide="checked">
        I hide when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      body {
        overflow: hidden;
        perspective: 1000px;
      }

      .funky-show-hide.ng-hide-add {
        transform: rotateZ(0);
        transform-origin: right;
        transition: all 0.5s ease-in-out;
      }

      .funky-show-hide.ng-hide-add.ng-hide-add-active {
        transform: rotateZ(-135deg);
      }

      .funky-show-hide.ng-hide-remove {
        transform: rotateY(90deg);
        transform-origin: left;
        transition: all 0.5s ease;
      }

      .funky-show-hide.ng-hide-remove.ng-hide-remove-active {
        transform: rotateY(0);
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngHide', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(true);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(false);
      });
    </file>
  </example>
 */
var ngHideDirective = ['$animate', function($animate) {
  return {
    restrict: 'A',
    multiElement: true,
    link: function(scope, element, attr) {
      scope.$watch(attr.ngHide, function ngHideWatchAction(value) {
        // The comment inside of the ngShowDirective explains why we add and
        // remove a temporary class for the show/hide animation
        $animate[value ? 'addClass' : 'removeClass'](element,NG_HIDE_CLASS, {
          tempClasses: NG_HIDE_IN_PROGRESS_CLASS
        });
      });
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngStyle
 * @restrict AC
 *
 * @description
 * The `ngStyle` directive allows you to set CSS style on an HTML element conditionally.
 *
 * @knownIssue
 * You should not use {@link guide/interpolation interpolation} in the value of the `style`
 * attribute, when using the `ngStyle` directive on the same element.
 * See {@link guide/interpolation#known-issues here} for more info.
 *
 * @element ANY
 * @param {expression} ngStyle
 *
 * {@link guide/expression Expression} which evals to an
 * object whose keys are CSS style names and values are corresponding values for those CSS
 * keys.
 *
 * Since some CSS style names are not valid keys for an object, they must be quoted.
 * See the 'background-color' style in the example below.
 *
 * @example
   <example name="ng-style">
     <file name="index.html">
        <input type="button" value="set color" ng-click="myStyle={color:'red'}">
        <input type="button" value="set background" ng-click="myStyle={'background-color':'blue'}">
        <input type="button" value="clear" ng-click="myStyle={}">
        <br/>
        <span ng-style="myStyle">Sample Text</span>
        <pre>myStyle={{myStyle}}</pre>
     </file>
     <file name="style.css">
       span {
         color: black;
       }
     </file>
     <file name="protractor.js" type="protractor">
       var colorSpan = element(by.css('span'));

       it('should check ng-style', function() {
         expect(colorSpan.getCssValue('color')).toBe('rgba(0, 0, 0, 1)');
         element(by.css('input[value=\'set color\']')).click();
         expect(colorSpan.getCssValue('color')).toBe('rgba(255, 0, 0, 1)');
         element(by.css('input[value=clear]')).click();
         expect(colorSpan.getCssValue('color')).toBe('rgba(0, 0, 0, 1)');
       });
     </file>
   </example>
 */
var ngStyleDirective = ngDirective(function(scope, element, attr) {
  scope.$watch(attr.ngStyle, function ngStyleWatchAction(newStyles, oldStyles) {
    if (oldStyles && (newStyles !== oldStyles)) {
      forEach(oldStyles, function(val, style) { element.css(style, '');});
    }
    if (newStyles) element.css(newStyles);
  }, true);
});

/**
 * @ngdoc directive
 * @name ngSwitch
 * @restrict EA
 *
 * @description
 * The `ngSwitch` directive is used to conditionally swap DOM structure on your template based on a scope expression.
 * Elements within `ngSwitch` but without `ngSwitchWhen` or `ngSwitchDefault` directives will be preserved at the location
 * as specified in the template.
 *
 * The directive itself works similar to ngInclude, however, instead of downloading template code (or loading it
 * from the template cache), `ngSwitch` simply chooses one of the nested elements and makes it visible based on which element
 * matches the value obtained from the evaluated expression. In other words, you define a container element
 * (where you place the directive), place an expression on the **`on="..."` attribute**
 * (or the **`ng-switch="..."` attribute**), define any inner elements inside of the directive and place
 * a when attribute per element. The when attribute is used to inform ngSwitch which element to display when the on
 * expression is evaluated. If a matching expression is not found via a when attribute then an element with the default
 * attribute is displayed.
 *
 * <div class="alert alert-info">
 * Be aware that the attribute values to match against cannot be expressions. They are interpreted
 * as literal string values to match against.
 * For example, **`ng-switch-when="someVal"`** will match against the string `"someVal"` not against the
 * value of the expression `$scope.someVal`.
 * </div>

 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter}  | after the ngSwitch contents change and the matched child element is placed inside the container |
 * | {@link ng.$animate#leave leave}  | after the ngSwitch contents change and just before the former contents are removed from the DOM |
 *
 * @usage
 *
 * ```
 * <ANY ng-switch="expression">
 *   <ANY ng-switch-when="matchValue1">...</ANY>
 *   <ANY ng-switch-when="matchValue2">...</ANY>
 *   <ANY ng-switch-default>...</ANY>
 * </ANY>
 * ```
 *
 *
 * @scope
 * @priority 1200
 * @param {*} ngSwitch|on expression to match against <code>ng-switch-when</code>.
 * On child elements add:
 *
 * * `ngSwitchWhen`: the case statement to match against. If match then this
 *   case will be displayed. If the same match appears multiple times, all the
 *   elements will be displayed. It is possible to associate multiple values to
 *   the same `ngSwitchWhen` by defining the optional attribute
 *   `ngSwitchWhenSeparator`. The separator will be used to split the value of
 *   the `ngSwitchWhen` attribute into multiple tokens, and the element will show
 *   if any of the `ngSwitch` evaluates to any of these tokens.
 * * `ngSwitchDefault`: the default case when no other case match. If there
 *   are multiple default cases, all of them will be displayed when no other
 *   case match.
 *
 *
 * @example
  <example module="switchExample" deps="angular-animate.js" animations="true" name="ng-switch">
    <file name="index.html">
      <div ng-controller="ExampleController">
        <select ng-model="selection" ng-options="item for item in items">
        </select>
        <code>selection={{selection}}</code>
        <hr/>
        <div class="animate-switch-container"
          ng-switch on="selection">
            <div class="animate-switch" ng-switch-when="settings|options" ng-switch-when-separator="|">Settings Div</div>
            <div class="animate-switch" ng-switch-when="home">Home Span</div>
            <div class="animate-switch" ng-switch-default>default</div>
        </div>
      </div>
    </file>
    <file name="script.js">
      angular.module('switchExample', ['ngAnimate'])
        .controller('ExampleController', ['$scope', function($scope) {
          $scope.items = ['settings', 'home', 'options', 'other'];
          $scope.selection = $scope.items[0];
        }]);
    </file>
    <file name="animations.css">
      .animate-switch-container {
        position:relative;
        background:white;
        border:1px solid black;
        height:40px;
        overflow:hidden;
      }

      .animate-switch {
        padding:10px;
      }

      .animate-switch.ng-animate {
        transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;

        position:absolute;
        top:0;
        left:0;
        right:0;
        bottom:0;
      }

      .animate-switch.ng-leave.ng-leave-active,
      .animate-switch.ng-enter {
        top:-50px;
      }
      .animate-switch.ng-leave,
      .animate-switch.ng-enter.ng-enter-active {
        top:0;
      }
    </file>
    <file name="protractor.js" type="protractor">
      var switchElem = element(by.css('[ng-switch]'));
      var select = element(by.model('selection'));

      it('should start in settings', function() {
        expect(switchElem.getText()).toMatch(/Settings Div/);
      });
      it('should change to home', function() {
        select.all(by.css('option')).get(1).click();
        expect(switchElem.getText()).toMatch(/Home Span/);
      });
      it('should change to settings via "options"', function() {
        select.all(by.css('option')).get(2).click();
        expect(switchElem.getText()).toMatch(/Settings Div/);
      });
      it('should select default', function() {
        select.all(by.css('option')).get(3).click();
        expect(switchElem.getText()).toMatch(/default/);
      });
    </file>
  </example>
 */
var ngSwitchDirective = ['$animate', '$compile', function($animate, $compile) {
  return {
    require: 'ngSwitch',

    // asks for $scope to fool the BC controller module
    controller: ['$scope', function NgSwitchController() {
     this.cases = {};
    }],
    link: function(scope, element, attr, ngSwitchController) {
      var watchExpr = attr.ngSwitch || attr.on,
          selectedTranscludes = [],
          selectedElements = [],
          previousLeaveAnimations = [],
          selectedScopes = [];

      var spliceFactory = function(array, index) {
          return function(response) {
            if (response !== false) array.splice(index, 1);
          };
      };

      scope.$watch(watchExpr, function ngSwitchWatchAction(value) {
        var i, ii;

        // Start with the last, in case the array is modified during the loop
        while (previousLeaveAnimations.length) {
          $animate.cancel(previousLeaveAnimations.pop());
        }

        for (i = 0, ii = selectedScopes.length; i < ii; ++i) {
          var selected = getBlockNodes(selectedElements[i].clone);
          selectedScopes[i].$destroy();
          var runner = previousLeaveAnimations[i] = $animate.leave(selected);
          runner.done(spliceFactory(previousLeaveAnimations, i));
        }

        selectedElements.length = 0;
        selectedScopes.length = 0;

        if ((selectedTranscludes = ngSwitchController.cases['!' + value] || ngSwitchController.cases['?'])) {
          forEach(selectedTranscludes, function(selectedTransclude) {
            selectedTransclude.transclude(function(caseElement, selectedScope) {
              selectedScopes.push(selectedScope);
              var anchor = selectedTransclude.element;
              caseElement[caseElement.length++] = $compile.$$createComment('end ngSwitchWhen');
              var block = { clone: caseElement };

              selectedElements.push(block);
              $animate.enter(caseElement, anchor.parent(), anchor);
            });
          });
        }
      });
    }
  };
}];

var ngSwitchWhenDirective = ngDirective({
  transclude: 'element',
  priority: 1200,
  require: '^ngSwitch',
  multiElement: true,
  link: function(scope, element, attrs, ctrl, $transclude) {

    var cases = attrs.ngSwitchWhen.split(attrs.ngSwitchWhenSeparator).sort().filter(
      // Filter duplicate cases
      function(element, index, array) { return array[index - 1] !== element; }
    );

    forEach(cases, function(whenCase) {
      ctrl.cases['!' + whenCase] = (ctrl.cases['!' + whenCase] || []);
      ctrl.cases['!' + whenCase].push({ transclude: $transclude, element: element });
    });
  }
});

var ngSwitchDefaultDirective = ngDirective({
  transclude: 'element',
  priority: 1200,
  require: '^ngSwitch',
  multiElement: true,
  link: function(scope, element, attr, ctrl, $transclude) {
    ctrl.cases['?'] = (ctrl.cases['?'] || []);
    ctrl.cases['?'].push({ transclude: $transclude, element: element });
   }
});

/**
 * @ngdoc directive
 * @name ngTransclude
 * @restrict EAC
 *
 * @description
 * Directive that marks the insertion point for the transcluded DOM of the nearest parent directive that uses transclusion.
 *
 * You can specify that you want to insert a named transclusion slot, instead of the default slot, by providing the slot name
 * as the value of the `ng-transclude` or `ng-transclude-slot` attribute.
 *
 * If the transcluded content is not empty (i.e. contains one or more DOM nodes, including whitespace text nodes), any existing
 * content of this element will be removed before the transcluded content is inserted.
 * If the transcluded content is empty (or only whitespace), the existing content is left intact. This lets you provide fallback
 * content in the case that no transcluded content is provided.
 *
 * @element ANY
 *
 * @param {string} ngTransclude|ngTranscludeSlot the name of the slot to insert at this point. If this is not provided, is empty
 *                                               or its value is the same as the name of the attribute then the default slot is used.
 *
 * @example
 * ### Basic transclusion
 * This example demonstrates basic transclusion of content into a component directive.
 * <example name="simpleTranscludeExample" module="transcludeExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('transcludeExample', [])
 *        .directive('pane', function(){
 *           return {
 *             restrict: 'E',
 *             transclude: true,
 *             scope: { title:'@' },
 *             template: '<div style="border: 1px solid black;">' +
 *                         '<div style="background-color: gray">{{title}}</div>' +
 *                         '<ng-transclude></ng-transclude>' +
 *                       '</div>'
 *           };
 *       })
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         $scope.title = 'Lorem Ipsum';
 *         $scope.text = 'Neque porro quisquam est qui dolorem ipsum quia dolor...';
 *       }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <input ng-model="title" aria-label="title"> <br/>
 *       <textarea ng-model="text" aria-label="text"></textarea> <br/>
 *       <pane title="{{title}}"><span>{{text}}</span></pane>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *      it('should have transcluded', function() {
 *        var titleElement = element(by.model('title'));
 *        titleElement.clear();
 *        titleElement.sendKeys('TITLE');
 *        var textElement = element(by.model('text'));
 *        textElement.clear();
 *        textElement.sendKeys('TEXT');
 *        expect(element(by.binding('title')).getText()).toEqual('TITLE');
 *        expect(element(by.binding('text')).getText()).toEqual('TEXT');
 *      });
 *   </file>
 * </example>
 *
 * @example
 * ### Transclude fallback content
 * This example shows how to use `NgTransclude` with fallback content, that
 * is displayed if no transcluded content is provided.
 *
 * <example module="transcludeFallbackContentExample" name="ng-transclude">
 * <file name="index.html">
 * <script>
 * angular.module('transcludeFallbackContentExample', [])
 * .directive('myButton', function(){
 *             return {
 *               restrict: 'E',
 *               transclude: true,
 *               scope: true,
 *               template: '<button style="cursor: pointer;">' +
 *                           '<ng-transclude>' +
 *                             '<b style="color: red;">Button1</b>' +
 *                           '</ng-transclude>' +
 *                         '</button>'
 *             };
 *         });
 * </script>
 * <!-- fallback button content -->
 * <my-button id="fallback"></my-button>
 * <!-- modified button content -->
 * <my-button id="modified">
 *   <i style="color: green;">Button2</i>
 * </my-button>
 * </file>
 * <file name="protractor.js" type="protractor">
 * it('should have different transclude element content', function() {
 *          expect(element(by.id('fallback')).getText()).toBe('Button1');
 *          expect(element(by.id('modified')).getText()).toBe('Button2');
 *        });
 * </file>
 * </example>
 *
 * @example
 * ### Multi-slot transclusion
 * This example demonstrates using multi-slot transclusion in a component directive.
 * <example name="multiSlotTranscludeExample" module="multiSlotTranscludeExample">
 *   <file name="index.html">
 *    <style>
 *      .title, .footer {
 *        background-color: gray
 *      }
 *    </style>
 *    <div ng-controller="ExampleController">
 *      <input ng-model="title" aria-label="title"> <br/>
 *      <textarea ng-model="text" aria-label="text"></textarea> <br/>
 *      <pane>
 *        <pane-title><a ng-href="{{link}}">{{title}}</a></pane-title>
 *        <pane-body><p>{{text}}</p></pane-body>
 *      </pane>
 *    </div>
 *   </file>
 *   <file name="app.js">
 *    angular.module('multiSlotTranscludeExample', [])
 *     .directive('pane', function() {
 *        return {
 *          restrict: 'E',
 *          transclude: {
 *            'title': '?paneTitle',
 *            'body': 'paneBody',
 *            'footer': '?paneFooter'
 *          },
 *          template: '<div style="border: 1px solid black;">' +
 *                      '<div class="title" ng-transclude="title">Fallback Title</div>' +
 *                      '<div ng-transclude="body"></div>' +
 *                      '<div class="footer" ng-transclude="footer">Fallback Footer</div>' +
 *                    '</div>'
 *        };
 *    })
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.title = 'Lorem Ipsum';
 *      $scope.link = 'https://google.com';
 *      $scope.text = 'Neque porro quisquam est qui dolorem ipsum quia dolor...';
 *    }]);
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *      it('should have transcluded the title and the body', function() {
 *        var titleElement = element(by.model('title'));
 *        titleElement.clear();
 *        titleElement.sendKeys('TITLE');
 *        var textElement = element(by.model('text'));
 *        textElement.clear();
 *        textElement.sendKeys('TEXT');
 *        expect(element(by.css('.title')).getText()).toEqual('TITLE');
 *        expect(element(by.binding('text')).getText()).toEqual('TEXT');
 *        expect(element(by.css('.footer')).getText()).toEqual('Fallback Footer');
 *      });
 *   </file>
 * </example>
 */
var ngTranscludeMinErr = minErr('ngTransclude');
var ngTranscludeDirective = ['$compile', function($compile) {
  return {
    restrict: 'EAC',
    terminal: true,
    compile: function ngTranscludeCompile(tElement) {

      // Remove and cache any original content to act as a fallback
      var fallbackLinkFn = $compile(tElement.contents());
      tElement.empty();

      return function ngTranscludePostLink($scope, $element, $attrs, controller, $transclude) {

        if (!$transclude) {
          throw ngTranscludeMinErr('orphan',
          'Illegal use of ngTransclude directive in the template! ' +
          'No parent directive that requires a transclusion found. ' +
          'Element: {0}',
          startingTag($element));
        }


        // If the attribute is of the form: `ng-transclude="ng-transclude"` then treat it like the default
        if ($attrs.ngTransclude === $attrs.$attr.ngTransclude) {
          $attrs.ngTransclude = '';
        }
        var slotName = $attrs.ngTransclude || $attrs.ngTranscludeSlot;

        // If the slot is required and no transclusion content is provided then this call will throw an error
        $transclude(ngTranscludeCloneAttachFn, null, slotName);

        // If the slot is optional and no transclusion content is provided then use the fallback content
        if (slotName && !$transclude.isSlotFilled(slotName)) {
          useFallbackContent();
        }

        function ngTranscludeCloneAttachFn(clone, transcludedScope) {
          if (clone.length && notWhitespace(clone)) {
            $element.append(clone);
          } else {
            useFallbackContent();
            // There is nothing linked against the transcluded scope since no content was available,
            // so it should be safe to clean up the generated scope.
            transcludedScope.$destroy();
          }
        }

        function useFallbackContent() {
          // Since this is the fallback content rather than the transcluded content,
          // we link against the scope of this directive rather than the transcluded scope
          fallbackLinkFn($scope, function(clone) {
            $element.append(clone);
          });
        }

        function notWhitespace(nodes) {
          for (var i = 0, ii = nodes.length; i < ii; i++) {
            var node = nodes[i];
            if (node.nodeType !== NODE_TYPE_TEXT || node.nodeValue.trim()) {
              return true;
            }
          }
        }
      };
    }
  };
}];

/**
 * @ngdoc directive
 * @name script
 * @restrict E
 *
 * @description
 * Load the content of a `<script>` element into {@link ng.$templateCache `$templateCache`}, so that the
 * template can be used by {@link ng.directive:ngInclude `ngInclude`},
 * {@link ngRoute.directive:ngView `ngView`}, or {@link guide/directive directives}. The type of the
 * `<script>` element must be specified as `text/ng-template`, and a cache name for the template must be
 * assigned through the element's `id`, which can then be used as a directive's `templateUrl`.
 *
 * @param {string} type Must be set to `'text/ng-template'`.
 * @param {string} id Cache name of the template.
 *
 * @example
  <example  name="script-tag">
    <file name="index.html">
      <script type="text/ng-template" id="/tpl.html">
        Content of the template.
      </script>

      <a ng-click="currentTpl='/tpl.html'" id="tpl-link">Load inlined template</a>
      <div id="tpl-content" ng-include src="currentTpl"></div>
    </file>
    <file name="protractor.js" type="protractor">
      it('should load template defined inside script tag', function() {
        element(by.css('#tpl-link')).click();
        expect(element(by.css('#tpl-content')).getText()).toMatch(/Content of the template/);
      });
    </file>
  </example>
 */
var scriptDirective = ['$templateCache', function($templateCache) {
  return {
    restrict: 'E',
    terminal: true,
    compile: function(element, attr) {
      if (attr.type === 'text/ng-template') {
        var templateUrl = attr.id,
            text = element[0].text;

        $templateCache.put(templateUrl, text);
      }
    }
  };
}];

/* exported selectDirective, optionDirective */

var noopNgModelController = { $setViewValue: noop, $render: noop };

function setOptionSelectedStatus(optionEl, value) {
  optionEl.prop('selected', value); // needed for IE
  /**
   * When unselecting an option, setting the property to null / false should be enough
   * However, screenreaders might react to the selected attribute instead, see
   * https://github.com/angular/angular.js/issues/14419
   * Note: "selected" is a boolean attr and will be removed when the "value" arg in attr() is false
   * or null
   */
  optionEl.attr('selected', value);
}

/**
 * @ngdoc type
 * @name  select.SelectController
 * @description
 * The controller for the `<select>` directive. This provides support for reading
 * and writing the selected value(s) of the control and also coordinates dynamically
 * added `<option>` elements, perhaps by an `ngRepeat` directive.
 */
var SelectController =
        ['$element', '$scope', /** @this */ function($element, $scope) {

  var self = this,
      optionsMap = new NgMap();

  self.selectValueMap = {}; // Keys are the hashed values, values the original values

  // If the ngModel doesn't get provided then provide a dummy noop version to prevent errors
  self.ngModelCtrl = noopNgModelController;
  self.multiple = false;

  // The "unknown" option is one that is prepended to the list if the viewValue
  // does not match any of the options. When it is rendered the value of the unknown
  // option is '? XXX ?' where XXX is the hashKey of the value that is not known.
  //
  // We can't just jqLite('<option>') since jqLite is not smart enough
  // to create it in <select> and IE barfs otherwise.
  self.unknownOption = jqLite(window.document.createElement('option'));

  // The empty option is an option with the value '' that te application developer can
  // provide inside the select. When the model changes to a value that doesn't match an option,
  // it is selected - so if an empty option is provided, no unknown option is generated.
  // However, the empty option is not removed when the model matches an option. It is always selectable
  // and indicates that a "null" selection has been made.
  self.hasEmptyOption = false;
  self.emptyOption = undefined;

  self.renderUnknownOption = function(val) {
    var unknownVal = self.generateUnknownOptionValue(val);
    self.unknownOption.val(unknownVal);
    $element.prepend(self.unknownOption);
    setOptionSelectedStatus(self.unknownOption, true);
    $element.val(unknownVal);
  };

  self.updateUnknownOption = function(val) {
    var unknownVal = self.generateUnknownOptionValue(val);
    self.unknownOption.val(unknownVal);
    setOptionSelectedStatus(self.unknownOption, true);
    $element.val(unknownVal);
  };

  self.generateUnknownOptionValue = function(val) {
    return '? ' + hashKey(val) + ' ?';
  };

  self.removeUnknownOption = function() {
    if (self.unknownOption.parent()) self.unknownOption.remove();
  };

  self.selectEmptyOption = function() {
    if (self.emptyOption) {
      $element.val('');
      setOptionSelectedStatus(self.emptyOption, true);
    }
  };

  self.unselectEmptyOption = function() {
    if (self.hasEmptyOption) {
      self.emptyOption.removeAttr('selected');
    }
  };

  $scope.$on('$destroy', function() {
    // disable unknown option so that we don't do work when the whole select is being destroyed
    self.renderUnknownOption = noop;
  });

  // Read the value of the select control, the implementation of this changes depending
  // upon whether the select can have multiple values and whether ngOptions is at work.
  self.readValue = function readSingleValue() {
    var val = $element.val();
    // ngValue added option values are stored in the selectValueMap, normal interpolations are not
    var realVal = val in self.selectValueMap ? self.selectValueMap[val] : val;

    if (self.hasOption(realVal)) {
      return realVal;
    }

    return null;
  };


  // Write the value to the select control, the implementation of this changes depending
  // upon whether the select can have multiple values and whether ngOptions is at work.
  self.writeValue = function writeSingleValue(value) {
    // Make sure to remove the selected attribute from the previously selected option
    // Otherwise, screen readers might get confused
    var currentlySelectedOption = $element[0].options[$element[0].selectedIndex];
    if (currentlySelectedOption) setOptionSelectedStatus(jqLite(currentlySelectedOption), false);

    if (self.hasOption(value)) {
      self.removeUnknownOption();

      var hashedVal = hashKey(value);
      $element.val(hashedVal in self.selectValueMap ? hashedVal : value);

      // Set selected attribute and property on selected option for screen readers
      var selectedOption = $element[0].options[$element[0].selectedIndex];
      setOptionSelectedStatus(jqLite(selectedOption), true);
    } else {
      if (value == null && self.emptyOption) {
        self.removeUnknownOption();
        self.selectEmptyOption();
      } else if (self.unknownOption.parent().length) {
        self.updateUnknownOption(value);
      } else {
        self.renderUnknownOption(value);
      }
    }
  };


  // Tell the select control that an option, with the given value, has been added
  self.addOption = function(value, element) {
    // Skip comment nodes, as they only pollute the `optionsMap`
    if (element[0].nodeType === NODE_TYPE_COMMENT) return;

    assertNotHasOwnProperty(value, '"option value"');
    if (value === '') {
      self.hasEmptyOption = true;
      self.emptyOption = element;
    }
    var count = optionsMap.get(value) || 0;
    optionsMap.set(value, count + 1);
    // Only render at the end of a digest. This improves render performance when many options
    // are added during a digest and ensures all relevant options are correctly marked as selected
    scheduleRender();
  };

  // Tell the select control that an option, with the given value, has been removed
  self.removeOption = function(value) {
    var count = optionsMap.get(value);
    if (count) {
      if (count === 1) {
        optionsMap.delete(value);
        if (value === '') {
          self.hasEmptyOption = false;
          self.emptyOption = undefined;
        }
      } else {
        optionsMap.set(value, count - 1);
      }
    }
  };

  // Check whether the select control has an option matching the given value
  self.hasOption = function(value) {
    return !!optionsMap.get(value);
  };


  var renderScheduled = false;
  function scheduleRender() {
    if (renderScheduled) return;
    renderScheduled = true;
    $scope.$$postDigest(function() {
      renderScheduled = false;
      self.ngModelCtrl.$render();
    });
  }

  var updateScheduled = false;
  function scheduleViewValueUpdate(renderAfter) {
    if (updateScheduled) return;

    updateScheduled = true;

    $scope.$$postDigest(function() {
      if ($scope.$$destroyed) return;

      updateScheduled = false;
      self.ngModelCtrl.$setViewValue(self.readValue());
      if (renderAfter) self.ngModelCtrl.$render();
    });
  }


  self.registerOption = function(optionScope, optionElement, optionAttrs, interpolateValueFn, interpolateTextFn) {

    if (optionAttrs.$attr.ngValue) {
      // The value attribute is set by ngValue
      var oldVal, hashedVal = NaN;
      optionAttrs.$observe('value', function valueAttributeObserveAction(newVal) {

        var removal;
        var previouslySelected = optionElement.prop('selected');

        if (isDefined(hashedVal)) {
          self.removeOption(oldVal);
          delete self.selectValueMap[hashedVal];
          removal = true;
        }

        hashedVal = hashKey(newVal);
        oldVal = newVal;
        self.selectValueMap[hashedVal] = newVal;
        self.addOption(newVal, optionElement);
        // Set the attribute directly instead of using optionAttrs.$set - this stops the observer
        // from firing a second time. Other $observers on value will also get the result of the
        // ngValue expression, not the hashed value
        optionElement.attr('value', hashedVal);

        if (removal && previouslySelected) {
          scheduleViewValueUpdate();
        }

      });
    } else if (interpolateValueFn) {
      // The value attribute is interpolated
      optionAttrs.$observe('value', function valueAttributeObserveAction(newVal) {
        // This method is overwritten in ngOptions and has side-effects!
        self.readValue();

        var removal;
        var previouslySelected = optionElement.prop('selected');

        if (isDefined(oldVal)) {
          self.removeOption(oldVal);
          removal = true;
        }
        oldVal = newVal;
        self.addOption(newVal, optionElement);

        if (removal && previouslySelected) {
          scheduleViewValueUpdate();
        }
      });
    } else if (interpolateTextFn) {
      // The text content is interpolated
      optionScope.$watch(interpolateTextFn, function interpolateWatchAction(newVal, oldVal) {
        optionAttrs.$set('value', newVal);
        var previouslySelected = optionElement.prop('selected');
        if (oldVal !== newVal) {
          self.removeOption(oldVal);
        }
        self.addOption(newVal, optionElement);

        if (oldVal && previouslySelected) {
          scheduleViewValueUpdate();
        }
      });
    } else {
      // The value attribute is static
      self.addOption(optionAttrs.value, optionElement);
    }


    optionAttrs.$observe('disabled', function(newVal) {

      // Since model updates will also select disabled options (like ngOptions),
      // we only have to handle options becoming disabled, not enabled

      if (newVal === 'true' || newVal && optionElement.prop('selected')) {
        if (self.multiple) {
          scheduleViewValueUpdate(true);
        } else {
          self.ngModelCtrl.$setViewValue(null);
          self.ngModelCtrl.$render();
        }
      }
    });

    optionElement.on('$destroy', function() {
      var currentValue = self.readValue();
      var removeValue = optionAttrs.value;

      self.removeOption(removeValue);
      scheduleRender();

      if (self.multiple && currentValue && currentValue.indexOf(removeValue) !== -1 ||
          currentValue === removeValue
      ) {
        // When multiple (selected) options are destroyed at the same time, we don't want
        // to run a model update for each of them. Instead, run a single update in the $$postDigest
        scheduleViewValueUpdate(true);
      }
    });
  };
}];

/**
 * @ngdoc directive
 * @name select
 * @restrict E
 *
 * @description
 * HTML `select` element with angular data-binding.
 *
 * The `select` directive is used together with {@link ngModel `ngModel`} to provide data-binding
 * between the scope and the `<select>` control (including setting default values).
 * It also handles dynamic `<option>` elements, which can be added using the {@link ngRepeat `ngRepeat}` or
 * {@link ngOptions `ngOptions`} directives.
 *
 * When an item in the `<select>` menu is selected, the value of the selected option will be bound
 * to the model identified by the `ngModel` directive. With static or repeated options, this is
 * the content of the `value` attribute or the textContent of the `<option>`, if the value attribute is missing.
 * Value and textContent can be interpolated.
 *
 * ## Matching model and option values
 *
 * In general, the match between the model and an option is evaluated by strictly comparing the model
 * value against the value of the available options.
 *
 * If you are setting the option value with the option's `value` attribute, or textContent, the
 * value will always be a `string` which means that the model value must also be a string.
 * Otherwise the `select` directive cannot match them correctly.
 *
 * To bind the model to a non-string value, you can use one of the following strategies:
 * - the {@link ng.ngOptions `ngOptions`} directive
 *   ({@link ng.select#using-select-with-ngoptions-and-setting-a-default-value})
 * - the {@link ng.ngValue `ngValue`} directive, which allows arbitrary expressions to be
 *   option values ({@link ng.select#using-ngvalue-to-bind-the-model-to-an-array-of-objects Example})
 * - model $parsers / $formatters to convert the string value
 *   ({@link ng.select#binding-select-to-a-non-string-value-via-ngmodel-parsing-formatting Example})
 *
 * If the viewValue of `ngModel` does not match any of the options, then the control
 * will automatically add an "unknown" option, which it then removes when the mismatch is resolved.
 *
 * Optionally, a single hard-coded `<option>` element, with the value set to an empty string, can
 * be nested into the `<select>` element. This element will then represent the `null` or "not selected"
 * option. See example below for demonstration.
 *
 * ## Choosing between `ngRepeat` and `ngOptions`
 *
 * In many cases, `ngRepeat` can be used on `<option>` elements instead of {@link ng.directive:ngOptions
 * ngOptions} to achieve a similar result. However, `ngOptions` provides some benefits:
 * - more flexibility in how the `<select>`'s model is assigned via the `select` **`as`** part of the
 * comprehension expression
 * - reduced memory consumption by not creating a new scope for each repeated instance
 * - increased render speed by creating the options in a documentFragment instead of individually
 *
 * Specifically, select with repeated options slows down significantly starting at 2000 options in
 * Chrome and Internet Explorer / Edge.
 *
 *
 * @param {string} ngModel Assignable angular expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} multiple Allows multiple options to be selected. The selected values will be
 *     bound to the model as an array.
 * @param {string=} required Sets `required` validation error key if the value is not entered.
 * @param {string=} ngRequired Adds required attribute and required validation constraint to
 * the element when the ngRequired expression evaluates to true. Use ngRequired instead of required
 * when you want to data-bind to the required attribute.
 * @param {string=} ngChange Angular expression to be executed when selected option(s) changes due to user
 *    interaction with the select element.
 * @param {string=} ngOptions sets the options that the select is populated with and defines what is
 * set on the model on selection. See {@link ngOptions `ngOptions`}.
 * @param {string=} ngAttrSize sets the size of the select element dynamically. Uses the
 * {@link guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes ngAttr} directive.
 *
 * @example
 * ### Simple `select` elements with static options
 *
 * <example name="static-select" module="staticSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="singleSelect"> Single select: </label><br>
 *     <select name="singleSelect" ng-model="data.singleSelect">
 *       <option value="option-1">Option 1</option>
 *       <option value="option-2">Option 2</option>
 *     </select><br>
 *
 *     <label for="singleSelect"> Single select with "not selected" option and dynamic option values: </label><br>
 *     <select name="singleSelect" id="singleSelect" ng-model="data.singleSelect">
 *       <option value="">---Please select---</option> <!-- not selected / blank option -->
 *       <option value="{{data.option1}}">Option 1</option> <!-- interpolation -->
 *       <option value="option-2">Option 2</option>
 *     </select><br>
 *     <button ng-click="forceUnknownOption()">Force unknown option</button><br>
 *     <tt>singleSelect = {{data.singleSelect}}</tt>
 *
 *     <hr>
 *     <label for="multipleSelect"> Multiple select: </label><br>
 *     <select name="multipleSelect" id="multipleSelect" ng-model="data.multipleSelect" multiple>
 *       <option value="option-1">Option 1</option>
 *       <option value="option-2">Option 2</option>
 *       <option value="option-3">Option 3</option>
 *     </select><br>
 *     <tt>multipleSelect = {{data.multipleSelect}}</tt><br/>
 *   </form>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('staticSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       singleSelect: null,
 *       multipleSelect: [],
 *       option1: 'option-1'
 *      };
 *
 *      $scope.forceUnknownOption = function() {
 *        $scope.data.singleSelect = 'nonsense';
 *      };
 *   }]);
 * </file>
 *</example>
 *
 * ### Using `ngRepeat` to generate `select` options
 * <example name="select-ngrepeat" module="ngrepeatSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="repeatSelect"> Repeat select: </label>
 *     <select name="repeatSelect" id="repeatSelect" ng-model="data.model">
 *       <option ng-repeat="option in data.availableOptions" value="{{option.id}}">{{option.name}}</option>
 *     </select>
 *   </form>
 *   <hr>
 *   <tt>model = {{data.model}}</tt><br/>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('ngrepeatSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       model: null,
 *       availableOptions: [
 *         {id: '1', name: 'Option A'},
 *         {id: '2', name: 'Option B'},
 *         {id: '3', name: 'Option C'}
 *       ]
 *      };
 *   }]);
 * </file>
 *</example>
 *
 * ### Using `ngValue` to bind the model to an array of objects
 * <example name="select-ngvalue" module="ngvalueSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="ngvalueselect"> ngvalue select: </label>
 *     <select size="6" name="ngvalueselect" ng-model="data.model" multiple>
 *       <option ng-repeat="option in data.availableOptions" ng-value="option.value">{{option.name}}</option>
 *     </select>
 *   </form>
 *   <hr>
 *   <pre>model = {{data.model | json}}</pre><br/>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('ngvalueSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       model: null,
 *       availableOptions: [
           {value: 'myString', name: 'string'},
           {value: 1, name: 'integer'},
           {value: true, name: 'boolean'},
           {value: null, name: 'null'},
           {value: {prop: 'value'}, name: 'object'},
           {value: ['a'], name: 'array'}
 *       ]
 *      };
 *   }]);
 * </file>
 *</example>
 *
 * ### Using `select` with `ngOptions` and setting a default value
 * See the {@link ngOptions ngOptions documentation} for more `ngOptions` usage examples.
 *
 * <example name="select-with-default-values" module="defaultValueSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="mySelect">Make a choice:</label>
 *     <select name="mySelect" id="mySelect"
 *       ng-options="option.name for option in data.availableOptions track by option.id"
 *       ng-model="data.selectedOption"></select>
 *   </form>
 *   <hr>
 *   <tt>option = {{data.selectedOption}}</tt><br/>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('defaultValueSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       availableOptions: [
 *         {id: '1', name: 'Option A'},
 *         {id: '2', name: 'Option B'},
 *         {id: '3', name: 'Option C'}
 *       ],
 *       selectedOption: {id: '3', name: 'Option C'} //This sets the default value of the select in the ui
 *       };
 *   }]);
 * </file>
 *</example>
 *
 *
 * ### Binding `select` to a non-string value via `ngModel` parsing / formatting
 *
 * <example name="select-with-non-string-options" module="nonStringSelect">
 *   <file name="index.html">
 *     <select ng-model="model.id" convert-to-number>
 *       <option value="0">Zero</option>
 *       <option value="1">One</option>
 *       <option value="2">Two</option>
 *     </select>
 *     {{ model }}
 *   </file>
 *   <file name="app.js">
 *     angular.module('nonStringSelect', [])
 *       .run(function($rootScope) {
 *         $rootScope.model = { id: 2 };
 *       })
 *       .directive('convertToNumber', function() {
 *         return {
 *           require: 'ngModel',
 *           link: function(scope, element, attrs, ngModel) {
 *             ngModel.$parsers.push(function(val) {
 *               return parseInt(val, 10);
 *             });
 *             ngModel.$formatters.push(function(val) {
 *               return '' + val;
 *             });
 *           }
 *         };
 *       });
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     it('should initialize to model', function() {
 *       expect(element(by.model('model.id')).$('option:checked').getText()).toEqual('Two');
 *     });
 *   </file>
 * </example>
 *
 */
var selectDirective = function() {

  return {
    restrict: 'E',
    require: ['select', '?ngModel'],
    controller: SelectController,
    priority: 1,
    link: {
      pre: selectPreLink,
      post: selectPostLink
    }
  };

  function selectPreLink(scope, element, attr, ctrls) {

      var selectCtrl = ctrls[0];
      var ngModelCtrl = ctrls[1];

      // if ngModel is not defined, we don't need to do anything but set the registerOption
      // function to noop, so options don't get added internally
      if (!ngModelCtrl) {
        selectCtrl.registerOption = noop;
        return;
      }


      selectCtrl.ngModelCtrl = ngModelCtrl;

      // When the selected item(s) changes we delegate getting the value of the select control
      // to the `readValue` method, which can be changed if the select can have multiple
      // selected values or if the options are being generated by `ngOptions`
      element.on('change', function() {
        selectCtrl.removeUnknownOption();
        scope.$apply(function() {
          ngModelCtrl.$setViewValue(selectCtrl.readValue());
        });
      });

      // If the select allows multiple values then we need to modify how we read and write
      // values from and to the control; also what it means for the value to be empty and
      // we have to add an extra watch since ngModel doesn't work well with arrays - it
      // doesn't trigger rendering if only an item in the array changes.
      if (attr.multiple) {
        selectCtrl.multiple = true;

        // Read value now needs to check each option to see if it is selected
        selectCtrl.readValue = function readMultipleValue() {
          var array = [];
          forEach(element.find('option'), function(option) {
            if (option.selected && !option.disabled) {
              var val = option.value;
              array.push(val in selectCtrl.selectValueMap ? selectCtrl.selectValueMap[val] : val);
            }
          });
          return array;
        };

        // Write value now needs to set the selected property of each matching option
        selectCtrl.writeValue = function writeMultipleValue(value) {
          forEach(element.find('option'), function(option) {
            var shouldBeSelected = !!value && (includes(value, option.value) ||
                                               includes(value, selectCtrl.selectValueMap[option.value]));
            var currentlySelected = option.selected;

            // IE and Edge, adding options to the selection via shift+click/UP/DOWN,
            // will de-select already selected options if "selected" on those options was set
            // more than once (i.e. when the options were already selected)
            // So we only modify the selected property if neccessary.
            // Note: this behavior cannot be replicated via unit tests because it only shows in the
            // actual user interface.
            if (shouldBeSelected !== currentlySelected) {
              setOptionSelectedStatus(jqLite(option), shouldBeSelected);
            }

          });
        };

        // we have to do it on each watch since ngModel watches reference, but
        // we need to work of an array, so we need to see if anything was inserted/removed
        var lastView, lastViewRef = NaN;
        scope.$watch(function selectMultipleWatch() {
          if (lastViewRef === ngModelCtrl.$viewValue && !equals(lastView, ngModelCtrl.$viewValue)) {
            lastView = shallowCopy(ngModelCtrl.$viewValue);
            ngModelCtrl.$render();
          }
          lastViewRef = ngModelCtrl.$viewValue;
        });

        // If we are a multiple select then value is now a collection
        // so the meaning of $isEmpty changes
        ngModelCtrl.$isEmpty = function(value) {
          return !value || value.length === 0;
        };

      }
    }

    function selectPostLink(scope, element, attrs, ctrls) {
      // if ngModel is not defined, we don't need to do anything
      var ngModelCtrl = ctrls[1];
      if (!ngModelCtrl) return;

      var selectCtrl = ctrls[0];

      // We delegate rendering to the `writeValue` method, which can be changed
      // if the select can have multiple selected values or if the options are being
      // generated by `ngOptions`.
      // This must be done in the postLink fn to prevent $render to be called before
      // all nodes have been linked correctly.
      ngModelCtrl.$render = function() {
        selectCtrl.writeValue(ngModelCtrl.$viewValue);
      };
    }
};


// The option directive is purely designed to communicate the existence (or lack of)
// of dynamically created (and destroyed) option elements to their containing select
// directive via its controller.
var optionDirective = ['$interpolate', function($interpolate) {
  return {
    restrict: 'E',
    priority: 100,
    compile: function(element, attr) {
      var interpolateValueFn, interpolateTextFn;

      if (isDefined(attr.ngValue)) {
        // Will be handled by registerOption
      } else if (isDefined(attr.value)) {
        // If the value attribute is defined, check if it contains an interpolation
        interpolateValueFn = $interpolate(attr.value, true);
      } else {
        // If the value attribute is not defined then we fall back to the
        // text content of the option element, which may be interpolated
        interpolateTextFn = $interpolate(element.text(), true);
        if (!interpolateTextFn) {
          attr.$set('value', element.text());
        }
      }

      return function(scope, element, attr) {
        // This is an optimization over using ^^ since we don't want to have to search
        // all the way to the root of the DOM for every single option element
        var selectCtrlName = '$selectController',
            parent = element.parent(),
            selectCtrl = parent.data(selectCtrlName) ||
              parent.parent().data(selectCtrlName); // in case we are in optgroup

        if (selectCtrl) {
          selectCtrl.registerOption(scope, element, attr, interpolateValueFn, interpolateTextFn);
        }
      };
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngRequired
 * @restrict A
 *
 * @description
 *
 * ngRequired adds the required {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for {@link input `input`} and {@link select `select`} controls, but can also be
 * applied to custom controls.
 *
 * The directive sets the `required` attribute on the element if the Angular expression inside
 * `ngRequired` evaluates to true. A special directive for setting `required` is necessary because we
 * cannot use interpolation inside `required`. See the {@link guide/interpolation interpolation guide}
 * for more info.
 *
 * The validator will set the `required` error key to true if the `required` attribute is set and
 * calling {@link ngModel.NgModelController#$isEmpty `NgModelController.$isEmpty`} with the
 * {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`} returns `true`. For example, the
 * `$isEmpty()` implementation for `input[text]` checks the length of the `$viewValue`. When developing
 * custom controls, `$isEmpty()` can be overwritten to account for a $viewValue that is not string-based.
 *
 * @example
 * <example name="ngRequiredDirective" module="ngRequiredExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngRequiredExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.required = true;
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="required">Toggle required: </label>
 *         <input type="checkbox" ng-model="required" id="required" />
 *         <br>
 *         <label for="input">This input must be filled if `required` is true: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-required="required" /><br>
 *         <hr>
 *         required error set? = <code>{{form.input.$error.required}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var required = element(by.binding('form.input.$error.required'));
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should set the required error', function() {
         expect(required.getText()).toContain('true');

         input.sendKeys('123');
         expect(required.getText()).not.toContain('true');
         expect(model.getText()).toContain('123');
       });
 *   </file>
 * </example>
 */
var requiredDirective = function() {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;
      attr.required = true; // force truthy in case we are on non input element

      ctrl.$validators.required = function(modelValue, viewValue) {
        return !attr.required || !ctrl.$isEmpty(viewValue);
      };

      attr.$observe('required', function() {
        ctrl.$validate();
      });
    }
  };
};

/**
 * @ngdoc directive
 * @name ngPattern
 *
 * @description
 *
 * ngPattern adds the pattern {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
 *
 * The validator sets the `pattern` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
 * does not match a RegExp which is obtained by evaluating the Angular expression given in the
 * `ngPattern` attribute value:
 * * If the expression evaluates to a RegExp object, then this is used directly.
 * * If the expression evaluates to a string, then it will be converted to a RegExp after wrapping it
 * in `^` and `$` characters. For instance, `"abc"` will be converted to `new RegExp('^abc$')`.
 *
 * <div class="alert alert-info">
 * **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
 * start at the index of the last search's match, thus not taking the whole input value into
 * account.
 * </div>
 *
 * <div class="alert alert-info">
 * **Note:** This directive is also added when the plain `pattern` attribute is used, with two
 * differences:
 * <ol>
 *   <li>
 *     `ngPattern` does not set the `pattern` attribute and therefore HTML5 constraint validation is
 *     not available.
 *   </li>
 *   <li>
 *     The `ngPattern` attribute must be an expression, while the `pattern` value must be
 *     interpolated.
 *   </li>
 * </ol>
 * </div>
 *
 * @example
 * <example name="ngPatternDirective" module="ngPatternExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngPatternExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.regex = '\\d+';
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="regex">Set a pattern (regex string): </label>
 *         <input type="text" ng-model="regex" id="regex" />
 *         <br>
 *         <label for="input">This input is restricted by the current pattern: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-pattern="regex" /><br>
 *         <hr>
 *         input valid? = <code>{{form.input.$valid}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should validate the input with the default pattern', function() {
         input.sendKeys('aaa');
         expect(model.getText()).not.toContain('aaa');

         input.clear().then(function() {
           input.sendKeys('123');
           expect(model.getText()).toContain('123');
         });
       });
 *   </file>
 * </example>
 */
var patternDirective = function() {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;

      var regexp, patternExp = attr.ngPattern || attr.pattern;
      attr.$observe('pattern', function(regex) {
        if (isString(regex) && regex.length > 0) {
          regex = new RegExp('^' + regex + '$');
        }

        if (regex && !regex.test) {
          throw minErr('ngPattern')('noregexp',
            'Expected {0} to be a RegExp but was {1}. Element: {2}', patternExp,
            regex, startingTag(elm));
        }

        regexp = regex || undefined;
        ctrl.$validate();
      });

      ctrl.$validators.pattern = function(modelValue, viewValue) {
        // HTML5 pattern constraint validates the input value, so we validate the viewValue
        return ctrl.$isEmpty(viewValue) || isUndefined(regexp) || regexp.test(viewValue);
      };
    }
  };
};

/**
 * @ngdoc directive
 * @name ngMaxlength
 *
 * @description
 *
 * ngMaxlength adds the maxlength {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
 *
 * The validator sets the `maxlength` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
 * is longer than the integer obtained by evaluating the Angular expression given in the
 * `ngMaxlength` attribute value.
 *
 * <div class="alert alert-info">
 * **Note:** This directive is also added when the plain `maxlength` attribute is used, with two
 * differences:
 * <ol>
 *   <li>
 *     `ngMaxlength` does not set the `maxlength` attribute and therefore HTML5 constraint
 *     validation is not available.
 *   </li>
 *   <li>
 *     The `ngMaxlength` attribute must be an expression, while the `maxlength` value must be
 *     interpolated.
 *   </li>
 * </ol>
 * </div>
 *
 * @example
 * <example name="ngMaxlengthDirective" module="ngMaxlengthExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngMaxlengthExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.maxlength = 5;
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="maxlength">Set a maxlength: </label>
 *         <input type="number" ng-model="maxlength" id="maxlength" />
 *         <br>
 *         <label for="input">This input is restricted by the current maxlength: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-maxlength="maxlength" /><br>
 *         <hr>
 *         input valid? = <code>{{form.input.$valid}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should validate the input with the default maxlength', function() {
         input.sendKeys('abcdef');
         expect(model.getText()).not.toContain('abcdef');

         input.clear().then(function() {
           input.sendKeys('abcde');
           expect(model.getText()).toContain('abcde');
         });
       });
 *   </file>
 * </example>
 */
var maxlengthDirective = function() {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;

      var maxlength = -1;
      attr.$observe('maxlength', function(value) {
        var intVal = toInt(value);
        maxlength = isNumberNaN(intVal) ? -1 : intVal;
        ctrl.$validate();
      });
      ctrl.$validators.maxlength = function(modelValue, viewValue) {
        return (maxlength < 0) || ctrl.$isEmpty(viewValue) || (viewValue.length <= maxlength);
      };
    }
  };
};

/**
 * @ngdoc directive
 * @name ngMinlength
 *
 * @description
 *
 * ngMinlength adds the minlength {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
 *
 * The validator sets the `minlength` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
 * is shorter than the integer obtained by evaluating the Angular expression given in the
 * `ngMinlength` attribute value.
 *
 * <div class="alert alert-info">
 * **Note:** This directive is also added when the plain `minlength` attribute is used, with two
 * differences:
 * <ol>
 *   <li>
 *     `ngMinlength` does not set the `minlength` attribute and therefore HTML5 constraint
 *     validation is not available.
 *   </li>
 *   <li>
 *     The `ngMinlength` value must be an expression, while the `minlength` value must be
 *     interpolated.
 *   </li>
 * </ol>
 * </div>
 *
 * @example
 * <example name="ngMinlengthDirective" module="ngMinlengthExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngMinlengthExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.minlength = 3;
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="minlength">Set a minlength: </label>
 *         <input type="number" ng-model="minlength" id="minlength" />
 *         <br>
 *         <label for="input">This input is restricted by the current minlength: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-minlength="minlength" /><br>
 *         <hr>
 *         input valid? = <code>{{form.input.$valid}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should validate the input with the default minlength', function() {
         input.sendKeys('ab');
         expect(model.getText()).not.toContain('ab');

         input.sendKeys('abc');
         expect(model.getText()).toContain('abc');
       });
 *   </file>
 * </example>
 */
var minlengthDirective = function() {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;

      var minlength = 0;
      attr.$observe('minlength', function(value) {
        minlength = toInt(value) || 0;
        ctrl.$validate();
      });
      ctrl.$validators.minlength = function(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || viewValue.length >= minlength;
      };
    }
  };
};

if (window.angular.bootstrap) {
  // AngularJS is already loaded, so we can return here...
  if (window.console) {
    console.log('WARNING: Tried to load angular more than once.');
  }
  return;
}

// try to bind to jquery now so that one can write jqLite(fn)
// but we will rebind on bootstrap again.
bindJQuery();

publishExternalAPI(angular);

angular.module("ngLocale", [], ["$provide", function($provide) {
var PLURAL_CATEGORY = {ZERO: "zero", ONE: "one", TWO: "two", FEW: "few", MANY: "many", OTHER: "other"};
function getDecimals(n) {
  n = n + '';
  var i = n.indexOf('.');
  return (i == -1) ? 0 : n.length - i - 1;
}

function getVF(n, opt_precision) {
  var v = opt_precision;

  if (undefined === v) {
    v = Math.min(getDecimals(n), 3);
  }

  var base = Math.pow(10, v);
  var f = ((n * base) | 0) % base;
  return {v: v, f: f};
}

$provide.value("$locale", {
  "DATETIME_FORMATS": {
    "AMPMS": [
      "AM",
      "PM"
    ],
    "DAY": [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday"
    ],
    "ERANAMES": [
      "Before Christ",
      "Anno Domini"
    ],
    "ERAS": [
      "BC",
      "AD"
    ],
    "FIRSTDAYOFWEEK": 6,
    "MONTH": [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ],
    "SHORTDAY": [
      "Sun",
      "Mon",
      "Tue",
      "Wed",
      "Thu",
      "Fri",
      "Sat"
    ],
    "SHORTMONTH": [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ],
    "STANDALONEMONTH": [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ],
    "WEEKENDRANGE": [
      5,
      6
    ],
    "fullDate": "EEEE, MMMM d, y",
    "longDate": "MMMM d, y",
    "medium": "MMM d, y h:mm:ss a",
    "mediumDate": "MMM d, y",
    "mediumTime": "h:mm:ss a",
    "short": "M/d/yy h:mm a",
    "shortDate": "M/d/yy",
    "shortTime": "h:mm a"
  },
  "NUMBER_FORMATS": {
    "CURRENCY_SYM": "$",
    "DECIMAL_SEP": ".",
    "GROUP_SEP": ",",
    "PATTERNS": [
      {
        "gSize": 3,
        "lgSize": 3,
        "maxFrac": 3,
        "minFrac": 0,
        "minInt": 1,
        "negPre": "-",
        "negSuf": "",
        "posPre": "",
        "posSuf": ""
      },
      {
        "gSize": 3,
        "lgSize": 3,
        "maxFrac": 2,
        "minFrac": 2,
        "minInt": 1,
        "negPre": "-\u00a4",
        "negSuf": "",
        "posPre": "\u00a4",
        "posSuf": ""
      }
    ]
  },
  "id": "en-us",
  "localeID": "en_US",
  "pluralCat": function(n, opt_precision) {  var i = n | 0;  var vf = getVF(n, opt_precision);  if (i == 1 && vf.v == 0) {    return PLURAL_CATEGORY.ONE;  }  return PLURAL_CATEGORY.OTHER;}
});
}]);

  jqLite(function() {
    angularInit(window.document, bootstrap);
  });

})(window);

!window.angular.$$csp().noInlineStyle && window.angular.element(document.head).prepend('<style type="text/css">@charset "UTF-8";[ng\\:cloak],[ng-cloak],[data-ng-cloak],[x-ng-cloak],.ng-cloak,.x-ng-cloak,.ng-hide:not(.ng-hide-animate){display:none !important;}ng\\:form{display:block;}.ng-animate-shim{visibility:hidden;}.ng-anchor{position:absolute;}</style>');
/**
 * @license AngularJS v1.6.3
 * (c) 2010-2017 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

var ELEMENT_NODE = 1;
var COMMENT_NODE = 8;

var ADD_CLASS_SUFFIX = '-add';
var REMOVE_CLASS_SUFFIX = '-remove';
var EVENT_CLASS_PREFIX = 'ng-';
var ACTIVE_CLASS_SUFFIX = '-active';
var PREPARE_CLASS_SUFFIX = '-prepare';

var NG_ANIMATE_CLASSNAME = 'ng-animate';
var NG_ANIMATE_CHILDREN_DATA = '$$ngAnimateChildren';

// Detect proper transitionend/animationend event names.
var CSS_PREFIX = '', TRANSITION_PROP, TRANSITIONEND_EVENT, ANIMATION_PROP, ANIMATIONEND_EVENT;

// If unprefixed events are not supported but webkit-prefixed are, use the latter.
// Otherwise, just use W3C names, browsers not supporting them at all will just ignore them.
// Note: Chrome implements `window.onwebkitanimationend` and doesn't implement `window.onanimationend`
// but at the same time dispatches the `animationend` event and not `webkitAnimationEnd`.
// Register both events in case `window.onanimationend` is not supported because of that,
// do the same for `transitionend` as Safari is likely to exhibit similar behavior.
// Also, the only modern browser that uses vendor prefixes for transitions/keyframes is webkit
// therefore there is no reason to test anymore for other vendor prefixes:
// http://caniuse.com/#search=transition
if ((window.ontransitionend === undefined) && (window.onwebkittransitionend !== undefined)) {
  CSS_PREFIX = '-webkit-';
  TRANSITION_PROP = 'WebkitTransition';
  TRANSITIONEND_EVENT = 'webkitTransitionEnd transitionend';
} else {
  TRANSITION_PROP = 'transition';
  TRANSITIONEND_EVENT = 'transitionend';
}

if ((window.onanimationend === undefined) && (window.onwebkitanimationend !== undefined)) {
  CSS_PREFIX = '-webkit-';
  ANIMATION_PROP = 'WebkitAnimation';
  ANIMATIONEND_EVENT = 'webkitAnimationEnd animationend';
} else {
  ANIMATION_PROP = 'animation';
  ANIMATIONEND_EVENT = 'animationend';
}

var DURATION_KEY = 'Duration';
var PROPERTY_KEY = 'Property';
var DELAY_KEY = 'Delay';
var TIMING_KEY = 'TimingFunction';
var ANIMATION_ITERATION_COUNT_KEY = 'IterationCount';
var ANIMATION_PLAYSTATE_KEY = 'PlayState';
var SAFE_FAST_FORWARD_DURATION_VALUE = 9999;

var ANIMATION_DELAY_PROP = ANIMATION_PROP + DELAY_KEY;
var ANIMATION_DURATION_PROP = ANIMATION_PROP + DURATION_KEY;
var TRANSITION_DELAY_PROP = TRANSITION_PROP + DELAY_KEY;
var TRANSITION_DURATION_PROP = TRANSITION_PROP + DURATION_KEY;

var ngMinErr = angular.$$minErr('ng');
function assertArg(arg, name, reason) {
  if (!arg) {
    throw ngMinErr('areq', 'Argument \'{0}\' is {1}', (name || '?'), (reason || 'required'));
  }
  return arg;
}

function mergeClasses(a,b) {
  if (!a && !b) return '';
  if (!a) return b;
  if (!b) return a;
  if (isArray(a)) a = a.join(' ');
  if (isArray(b)) b = b.join(' ');
  return a + ' ' + b;
}

function packageStyles(options) {
  var styles = {};
  if (options && (options.to || options.from)) {
    styles.to = options.to;
    styles.from = options.from;
  }
  return styles;
}

function pendClasses(classes, fix, isPrefix) {
  var className = '';
  classes = isArray(classes)
      ? classes
      : classes && isString(classes) && classes.length
          ? classes.split(/\s+/)
          : [];
  forEach(classes, function(klass, i) {
    if (klass && klass.length > 0) {
      className += (i > 0) ? ' ' : '';
      className += isPrefix ? fix + klass
                            : klass + fix;
    }
  });
  return className;
}

function removeFromArray(arr, val) {
  var index = arr.indexOf(val);
  if (val >= 0) {
    arr.splice(index, 1);
  }
}

function stripCommentsFromElement(element) {
  if (element instanceof jqLite) {
    switch (element.length) {
      case 0:
        return element;

      case 1:
        // there is no point of stripping anything if the element
        // is the only element within the jqLite wrapper.
        // (it's important that we retain the element instance.)
        if (element[0].nodeType === ELEMENT_NODE) {
          return element;
        }
        break;

      default:
        return jqLite(extractElementNode(element));
    }
  }

  if (element.nodeType === ELEMENT_NODE) {
    return jqLite(element);
  }
}

function extractElementNode(element) {
  if (!element[0]) return element;
  for (var i = 0; i < element.length; i++) {
    var elm = element[i];
    if (elm.nodeType === ELEMENT_NODE) {
      return elm;
    }
  }
}

function $$addClass($$jqLite, element, className) {
  forEach(element, function(elm) {
    $$jqLite.addClass(elm, className);
  });
}

function $$removeClass($$jqLite, element, className) {
  forEach(element, function(elm) {
    $$jqLite.removeClass(elm, className);
  });
}

function applyAnimationClassesFactory($$jqLite) {
  return function(element, options) {
    if (options.addClass) {
      $$addClass($$jqLite, element, options.addClass);
      options.addClass = null;
    }
    if (options.removeClass) {
      $$removeClass($$jqLite, element, options.removeClass);
      options.removeClass = null;
    }
  };
}

function prepareAnimationOptions(options) {
  options = options || {};
  if (!options.$$prepared) {
    var domOperation = options.domOperation || noop;
    options.domOperation = function() {
      options.$$domOperationFired = true;
      domOperation();
      domOperation = noop;
    };
    options.$$prepared = true;
  }
  return options;
}

function applyAnimationStyles(element, options) {
  applyAnimationFromStyles(element, options);
  applyAnimationToStyles(element, options);
}

function applyAnimationFromStyles(element, options) {
  if (options.from) {
    element.css(options.from);
    options.from = null;
  }
}

function applyAnimationToStyles(element, options) {
  if (options.to) {
    element.css(options.to);
    options.to = null;
  }
}

function mergeAnimationDetails(element, oldAnimation, newAnimation) {
  var target = oldAnimation.options || {};
  var newOptions = newAnimation.options || {};

  var toAdd = (target.addClass || '') + ' ' + (newOptions.addClass || '');
  var toRemove = (target.removeClass || '') + ' ' + (newOptions.removeClass || '');
  var classes = resolveElementClasses(element.attr('class'), toAdd, toRemove);

  if (newOptions.preparationClasses) {
    target.preparationClasses = concatWithSpace(newOptions.preparationClasses, target.preparationClasses);
    delete newOptions.preparationClasses;
  }

  // noop is basically when there is no callback; otherwise something has been set
  var realDomOperation = target.domOperation !== noop ? target.domOperation : null;

  extend(target, newOptions);

  // TODO(matsko or sreeramu): proper fix is to maintain all animation callback in array and call at last,but now only leave has the callback so no issue with this.
  if (realDomOperation) {
    target.domOperation = realDomOperation;
  }

  if (classes.addClass) {
    target.addClass = classes.addClass;
  } else {
    target.addClass = null;
  }

  if (classes.removeClass) {
    target.removeClass = classes.removeClass;
  } else {
    target.removeClass = null;
  }

  oldAnimation.addClass = target.addClass;
  oldAnimation.removeClass = target.removeClass;

  return target;
}

function resolveElementClasses(existing, toAdd, toRemove) {
  var ADD_CLASS = 1;
  var REMOVE_CLASS = -1;

  var flags = {};
  existing = splitClassesToLookup(existing);

  toAdd = splitClassesToLookup(toAdd);
  forEach(toAdd, function(value, key) {
    flags[key] = ADD_CLASS;
  });

  toRemove = splitClassesToLookup(toRemove);
  forEach(toRemove, function(value, key) {
    flags[key] = flags[key] === ADD_CLASS ? null : REMOVE_CLASS;
  });

  var classes = {
    addClass: '',
    removeClass: ''
  };

  forEach(flags, function(val, klass) {
    var prop, allow;
    if (val === ADD_CLASS) {
      prop = 'addClass';
      allow = !existing[klass] || existing[klass + REMOVE_CLASS_SUFFIX];
    } else if (val === REMOVE_CLASS) {
      prop = 'removeClass';
      allow = existing[klass] || existing[klass + ADD_CLASS_SUFFIX];
    }
    if (allow) {
      if (classes[prop].length) {
        classes[prop] += ' ';
      }
      classes[prop] += klass;
    }
  });

  function splitClassesToLookup(classes) {
    if (isString(classes)) {
      classes = classes.split(' ');
    }

    var obj = {};
    forEach(classes, function(klass) {
      // sometimes the split leaves empty string values
      // incase extra spaces were applied to the options
      if (klass.length) {
        obj[klass] = true;
      }
    });
    return obj;
  }

  return classes;
}

function getDomNode(element) {
  return (element instanceof jqLite) ? element[0] : element;
}

function applyGeneratedPreparationClasses(element, event, options) {
  var classes = '';
  if (event) {
    classes = pendClasses(event, EVENT_CLASS_PREFIX, true);
  }
  if (options.addClass) {
    classes = concatWithSpace(classes, pendClasses(options.addClass, ADD_CLASS_SUFFIX));
  }
  if (options.removeClass) {
    classes = concatWithSpace(classes, pendClasses(options.removeClass, REMOVE_CLASS_SUFFIX));
  }
  if (classes.length) {
    options.preparationClasses = classes;
    element.addClass(classes);
  }
}

function clearGeneratedClasses(element, options) {
  if (options.preparationClasses) {
    element.removeClass(options.preparationClasses);
    options.preparationClasses = null;
  }
  if (options.activeClasses) {
    element.removeClass(options.activeClasses);
    options.activeClasses = null;
  }
}

function blockTransitions(node, duration) {
  // we use a negative delay value since it performs blocking
  // yet it doesn't kill any existing transitions running on the
  // same element which makes this safe for class-based animations
  var value = duration ? '-' + duration + 's' : '';
  applyInlineStyle(node, [TRANSITION_DELAY_PROP, value]);
  return [TRANSITION_DELAY_PROP, value];
}

function blockKeyframeAnimations(node, applyBlock) {
  var value = applyBlock ? 'paused' : '';
  var key = ANIMATION_PROP + ANIMATION_PLAYSTATE_KEY;
  applyInlineStyle(node, [key, value]);
  return [key, value];
}

function applyInlineStyle(node, styleTuple) {
  var prop = styleTuple[0];
  var value = styleTuple[1];
  node.style[prop] = value;
}

function concatWithSpace(a,b) {
  if (!a) return b;
  if (!b) return a;
  return a + ' ' + b;
}

var $$rAFSchedulerFactory = ['$$rAF', function($$rAF) {
  var queue, cancelFn;

  function scheduler(tasks) {
    // we make a copy since RAFScheduler mutates the state
    // of the passed in array variable and this would be difficult
    // to track down on the outside code
    queue = queue.concat(tasks);
    nextTick();
  }

  queue = scheduler.queue = [];

  /* waitUntilQuiet does two things:
   * 1. It will run the FINAL `fn` value only when an uncanceled RAF has passed through
   * 2. It will delay the next wave of tasks from running until the quiet `fn` has run.
   *
   * The motivation here is that animation code can request more time from the scheduler
   * before the next wave runs. This allows for certain DOM properties such as classes to
   * be resolved in time for the next animation to run.
   */
  scheduler.waitUntilQuiet = function(fn) {
    if (cancelFn) cancelFn();

    cancelFn = $$rAF(function() {
      cancelFn = null;
      fn();
      nextTick();
    });
  };

  return scheduler;

  function nextTick() {
    if (!queue.length) return;

    var items = queue.shift();
    for (var i = 0; i < items.length; i++) {
      items[i]();
    }

    if (!cancelFn) {
      $$rAF(function() {
        if (!cancelFn) nextTick();
      });
    }
  }
}];

/**
 * @ngdoc directive
 * @name ngAnimateChildren
 * @restrict AE
 * @element ANY
 *
 * @description
 *
 * ngAnimateChildren allows you to specify that children of this element should animate even if any
 * of the children's parents are currently animating. By default, when an element has an active `enter`, `leave`, or `move`
 * (structural) animation, child elements that also have an active structural animation are not animated.
 *
 * Note that even if `ngAnimateChildren` is set, no child animations will run when the parent element is removed from the DOM (`leave` animation).
 *
 *
 * @param {string} ngAnimateChildren If the value is empty, `true` or `on`,
 *     then child animations are allowed. If the value is `false`, child animations are not allowed.
 *
 * @example
 * <example module="ngAnimateChildren" name="ngAnimateChildren" deps="angular-animate.js" animations="true">
     <file name="index.html">
       <div ng-controller="MainController as main">
         <label>Show container? <input type="checkbox" ng-model="main.enterElement" /></label>
         <label>Animate children? <input type="checkbox" ng-model="main.animateChildren" /></label>
         <hr>
         <div ng-animate-children="{{main.animateChildren}}">
           <div ng-if="main.enterElement" class="container">
             List of items:
             <div ng-repeat="item in [0, 1, 2, 3]" class="item">Item {{item}}</div>
           </div>
         </div>
       </div>
     </file>
     <file name="animations.css">

      .container.ng-enter,
      .container.ng-leave {
        transition: all ease 1.5s;
      }

      .container.ng-enter,
      .container.ng-leave-active {
        opacity: 0;
      }

      .container.ng-leave,
      .container.ng-enter-active {
        opacity: 1;
      }

      .item {
        background: firebrick;
        color: #FFF;
        margin-bottom: 10px;
      }

      .item.ng-enter,
      .item.ng-leave {
        transition: transform 1.5s ease;
      }

      .item.ng-enter {
        transform: translateX(50px);
      }

      .item.ng-enter-active {
        transform: translateX(0);
      }
    </file>
    <file name="script.js">
      angular.module('ngAnimateChildren', ['ngAnimate'])
        .controller('MainController', function MainController() {
          this.animateChildren = false;
          this.enterElement = false;
        });
    </file>
  </example>
 */
var $$AnimateChildrenDirective = ['$interpolate', function($interpolate) {
  return {
    link: function(scope, element, attrs) {
      var val = attrs.ngAnimateChildren;
      if (isString(val) && val.length === 0) { //empty attribute
        element.data(NG_ANIMATE_CHILDREN_DATA, true);
      } else {
        // Interpolate and set the value, so that it is available to
        // animations that run right after compilation
        setData($interpolate(val)(scope));
        attrs.$observe('ngAnimateChildren', setData);
      }

      function setData(value) {
        value = value === 'on' || value === 'true';
        element.data(NG_ANIMATE_CHILDREN_DATA, value);
      }
    }
  };
}];

/* exported $AnimateCssProvider */

var ANIMATE_TIMER_KEY = '$$animateCss';

/**
 * @ngdoc service
 * @name $animateCss
 * @kind object
 *
 * @description
 * The `$animateCss` service is a useful utility to trigger customized CSS-based transitions/keyframes
 * from a JavaScript-based animation or directly from a directive. The purpose of `$animateCss` is NOT
 * to side-step how `$animate` and ngAnimate work, but the goal is to allow pre-existing animations or
 * directives to create more complex animations that can be purely driven using CSS code.
 *
 * Note that only browsers that support CSS transitions and/or keyframe animations are capable of
 * rendering animations triggered via `$animateCss` (bad news for IE9 and lower).
 *
 * ## Usage
 * Once again, `$animateCss` is designed to be used inside of a registered JavaScript animation that
 * is powered by ngAnimate. It is possible to use `$animateCss` directly inside of a directive, however,
 * any automatic control over cancelling animations and/or preventing animations from being run on
 * child elements will not be handled by Angular. For this to work as expected, please use `$animate` to
 * trigger the animation and then setup a JavaScript animation that injects `$animateCss` to trigger
 * the CSS animation.
 *
 * The example below shows how we can create a folding animation on an element using `ng-if`:
 *
 * ```html
 * <!-- notice the `fold-animation` CSS class -->
 * <div ng-if="onOff" class="fold-animation">
 *   This element will go BOOM
 * </div>
 * <button ng-click="onOff=true">Fold In</button>
 * ```
 *
 * Now we create the **JavaScript animation** that will trigger the CSS transition:
 *
 * ```js
 * ngModule.animation('.fold-animation', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element, doneFn) {
 *       var height = element[0].offsetHeight;
 *       return $animateCss(element, {
 *         from: { height:'0px' },
 *         to: { height:height + 'px' },
 *         duration: 1 // one second
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * ## More Advanced Uses
 *
 * `$animateCss` is the underlying code that ngAnimate uses to power **CSS-based animations** behind the scenes. Therefore CSS hooks
 * like `.ng-EVENT`, `.ng-EVENT-active`, `.ng-EVENT-stagger` are all features that can be triggered using `$animateCss` via JavaScript code.
 *
 * This also means that just about any combination of adding classes, removing classes, setting styles, dynamically setting a keyframe animation,
 * applying a hardcoded duration or delay value, changing the animation easing or applying a stagger animation are all options that work with
 * `$animateCss`. The service itself is smart enough to figure out the combination of options and examine the element styling properties in order
 * to provide a working animation that will run in CSS.
 *
 * The example below showcases a more advanced version of the `.fold-animation` from the example above:
 *
 * ```js
 * ngModule.animation('.fold-animation', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element, doneFn) {
 *       var height = element[0].offsetHeight;
 *       return $animateCss(element, {
 *         addClass: 'red large-text pulse-twice',
 *         easing: 'ease-out',
 *         from: { height:'0px' },
 *         to: { height:height + 'px' },
 *         duration: 1 // one second
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * Since we're adding/removing CSS classes then the CSS transition will also pick those up:
 *
 * ```css
 * /&#42; since a hardcoded duration value of 1 was provided in the JavaScript animation code,
 * the CSS classes below will be transitioned despite them being defined as regular CSS classes &#42;/
 * .red { background:red; }
 * .large-text { font-size:20px; }
 *
 * /&#42; we can also use a keyframe animation and $animateCss will make it work alongside the transition &#42;/
 * .pulse-twice {
 *   animation: 0.5s pulse linear 2;
 *   -webkit-animation: 0.5s pulse linear 2;
 * }
 *
 * @keyframes pulse {
 *   from { transform: scale(0.5); }
 *   to { transform: scale(1.5); }
 * }
 *
 * @-webkit-keyframes pulse {
 *   from { -webkit-transform: scale(0.5); }
 *   to { -webkit-transform: scale(1.5); }
 * }
 * ```
 *
 * Given this complex combination of CSS classes, styles and options, `$animateCss` will figure everything out and make the animation happen.
 *
 * ## How the Options are handled
 *
 * `$animateCss` is very versatile and intelligent when it comes to figuring out what configurations to apply to the element to ensure the animation
 * works with the options provided. Say for example we were adding a class that contained a keyframe value and we wanted to also animate some inline
 * styles using the `from` and `to` properties.
 *
 * ```js
 * var animator = $animateCss(element, {
 *   from: { background:'red' },
 *   to: { background:'blue' }
 * });
 * animator.start();
 * ```
 *
 * ```css
 * .rotating-animation {
 *   animation:0.5s rotate linear;
 *   -webkit-animation:0.5s rotate linear;
 * }
 *
 * @keyframes rotate {
 *   from { transform: rotate(0deg); }
 *   to { transform: rotate(360deg); }
 * }
 *
 * @-webkit-keyframes rotate {
 *   from { -webkit-transform: rotate(0deg); }
 *   to { -webkit-transform: rotate(360deg); }
 * }
 * ```
 *
 * The missing pieces here are that we do not have a transition set (within the CSS code nor within the `$animateCss` options) and the duration of the animation is
 * going to be detected from what the keyframe styles on the CSS class are. In this event, `$animateCss` will automatically create an inline transition
 * style matching the duration detected from the keyframe style (which is present in the CSS class that is being added) and then prepare both the transition
 * and keyframe animations to run in parallel on the element. Then when the animation is underway the provided `from` and `to` CSS styles will be applied
 * and spread across the transition and keyframe animation.
 *
 * ## What is returned
 *
 * `$animateCss` works in two stages: a preparation phase and an animation phase. Therefore when `$animateCss` is first called it will NOT actually
 * start the animation. All that is going on here is that the element is being prepared for the animation (which means that the generated CSS classes are
 * added and removed on the element). Once `$animateCss` is called it will return an object with the following properties:
 *
 * ```js
 * var animator = $animateCss(element, { ... });
 * ```
 *
 * Now what do the contents of our `animator` variable look like:
 *
 * ```js
 * {
 *   // starts the animation
 *   start: Function,
 *
 *   // ends (aborts) the animation
 *   end: Function
 * }
 * ```
 *
 * To actually start the animation we need to run `animation.start()` which will then return a promise that we can hook into to detect when the animation ends.
 * If we choose not to run the animation then we MUST run `animation.end()` to perform a cleanup on the element (since some CSS classes and styles may have been
 * applied to the element during the preparation phase). Note that all other properties such as duration, delay, transitions and keyframes are just properties
 * and that changing them will not reconfigure the parameters of the animation.
 *
 * ### runner.done() vs runner.then()
 * It is documented that `animation.start()` will return a promise object and this is true, however, there is also an additional method available on the
 * runner called `.done(callbackFn)`. The done method works the same as `.finally(callbackFn)`, however, it does **not trigger a digest to occur**.
 * Therefore, for performance reasons, it's always best to use `runner.done(callback)` instead of `runner.then()`, `runner.catch()` or `runner.finally()`
 * unless you really need a digest to kick off afterwards.
 *
 * Keep in mind that, to make this easier, ngAnimate has tweaked the JS animations API to recognize when a runner instance is returned from $animateCss
 * (so there is no need to call `runner.done(doneFn)` inside of your JavaScript animation code).
 * Check the {@link ngAnimate.$animateCss#usage animation code above} to see how this works.
 *
 * @param {DOMElement} element the element that will be animated
 * @param {object} options the animation-related options that will be applied during the animation
 *
 * * `event` - The DOM event (e.g. enter, leave, move). When used, a generated CSS class of `ng-EVENT` and `ng-EVENT-active` will be applied
 * to the element during the animation. Multiple events can be provided when spaces are used as a separator. (Note that this will not perform any DOM operation.)
 * * `structural` - Indicates that the `ng-` prefix will be added to the event class. Setting to `false` or omitting will turn `ng-EVENT` and
 * `ng-EVENT-active` in `EVENT` and `EVENT-active`. Unused if `event` is omitted.
 * * `easing` - The CSS easing value that will be applied to the transition or keyframe animation (or both).
 * * `transitionStyle` - The raw CSS transition style that will be used (e.g. `1s linear all`).
 * * `keyframeStyle` - The raw CSS keyframe animation style that will be used (e.g. `1s my_animation linear`).
 * * `from` - The starting CSS styles (a key/value object) that will be applied at the start of the animation.
 * * `to` - The ending CSS styles (a key/value object) that will be applied across the animation via a CSS transition.
 * * `addClass` - A space separated list of CSS classes that will be added to the element and spread across the animation.
 * * `removeClass` - A space separated list of CSS classes that will be removed from the element and spread across the animation.
 * * `duration` - A number value representing the total duration of the transition and/or keyframe (note that a value of 1 is 1000ms). If a value of `0`
 * is provided then the animation will be skipped entirely.
 * * `delay` - A number value representing the total delay of the transition and/or keyframe (note that a value of 1 is 1000ms). If a value of `true` is
 * used then whatever delay value is detected from the CSS classes will be mirrored on the elements styles (e.g. by setting delay true then the style value
 * of the element will be `transition-delay: DETECTED_VALUE`). Using `true` is useful when you want the CSS classes and inline styles to all share the same
 * CSS delay value.
 * * `stagger` - A numeric time value representing the delay between successively animated elements
 * ({@link ngAnimate#css-staggering-animations Click here to learn how CSS-based staggering works in ngAnimate.})
 * * `staggerIndex` - The numeric index representing the stagger item (e.g. a value of 5 is equal to the sixth item in the stagger; therefore when a
 *   `stagger` option value of `0.1` is used then there will be a stagger delay of `600ms`)
 * * `applyClassesEarly` - Whether or not the classes being added or removed will be used when detecting the animation. This is set by `$animate` when enter/leave/move animations are fired to ensure that the CSS classes are resolved in time. (Note that this will prevent any transitions from occurring on the classes being added and removed.)
 * * `cleanupStyles` - Whether or not the provided `from` and `to` styles will be removed once
 *    the animation is closed. This is useful for when the styles are used purely for the sake of
 *    the animation and do not have a lasting visual effect on the element (e.g. a collapse and open animation).
 *    By default this value is set to `false`.
 *
 * @return {object} an object with start and end methods and details about the animation.
 *
 * * `start` - The method to start the animation. This will return a `Promise` when called.
 * * `end` - This method will cancel the animation and remove all applied CSS classes and styles.
 */
var ONE_SECOND = 1000;

var ELAPSED_TIME_MAX_DECIMAL_PLACES = 3;
var CLOSING_TIME_BUFFER = 1.5;

var DETECT_CSS_PROPERTIES = {
  transitionDuration:      TRANSITION_DURATION_PROP,
  transitionDelay:         TRANSITION_DELAY_PROP,
  transitionProperty:      TRANSITION_PROP + PROPERTY_KEY,
  animationDuration:       ANIMATION_DURATION_PROP,
  animationDelay:          ANIMATION_DELAY_PROP,
  animationIterationCount: ANIMATION_PROP + ANIMATION_ITERATION_COUNT_KEY
};

var DETECT_STAGGER_CSS_PROPERTIES = {
  transitionDuration:      TRANSITION_DURATION_PROP,
  transitionDelay:         TRANSITION_DELAY_PROP,
  animationDuration:       ANIMATION_DURATION_PROP,
  animationDelay:          ANIMATION_DELAY_PROP
};

function getCssKeyframeDurationStyle(duration) {
  return [ANIMATION_DURATION_PROP, duration + 's'];
}

function getCssDelayStyle(delay, isKeyframeAnimation) {
  var prop = isKeyframeAnimation ? ANIMATION_DELAY_PROP : TRANSITION_DELAY_PROP;
  return [prop, delay + 's'];
}

function computeCssStyles($window, element, properties) {
  var styles = Object.create(null);
  var detectedStyles = $window.getComputedStyle(element) || {};
  forEach(properties, function(formalStyleName, actualStyleName) {
    var val = detectedStyles[formalStyleName];
    if (val) {
      var c = val.charAt(0);

      // only numerical-based values have a negative sign or digit as the first value
      if (c === '-' || c === '+' || c >= 0) {
        val = parseMaxTime(val);
      }

      // by setting this to null in the event that the delay is not set or is set directly as 0
      // then we can still allow for negative values to be used later on and not mistake this
      // value for being greater than any other negative value.
      if (val === 0) {
        val = null;
      }
      styles[actualStyleName] = val;
    }
  });

  return styles;
}

function parseMaxTime(str) {
  var maxValue = 0;
  var values = str.split(/\s*,\s*/);
  forEach(values, function(value) {
    // it's always safe to consider only second values and omit `ms` values since
    // getComputedStyle will always handle the conversion for us
    if (value.charAt(value.length - 1) === 's') {
      value = value.substring(0, value.length - 1);
    }
    value = parseFloat(value) || 0;
    maxValue = maxValue ? Math.max(value, maxValue) : value;
  });
  return maxValue;
}

function truthyTimingValue(val) {
  return val === 0 || val != null;
}

function getCssTransitionDurationStyle(duration, applyOnlyDuration) {
  var style = TRANSITION_PROP;
  var value = duration + 's';
  if (applyOnlyDuration) {
    style += DURATION_KEY;
  } else {
    value += ' linear all';
  }
  return [style, value];
}

function createLocalCacheLookup() {
  var cache = Object.create(null);
  return {
    flush: function() {
      cache = Object.create(null);
    },

    count: function(key) {
      var entry = cache[key];
      return entry ? entry.total : 0;
    },

    get: function(key) {
      var entry = cache[key];
      return entry && entry.value;
    },

    put: function(key, value) {
      if (!cache[key]) {
        cache[key] = { total: 1, value: value };
      } else {
        cache[key].total++;
      }
    }
  };
}

// we do not reassign an already present style value since
// if we detect the style property value again we may be
// detecting styles that were added via the `from` styles.
// We make use of `isDefined` here since an empty string
// or null value (which is what getPropertyValue will return
// for a non-existing style) will still be marked as a valid
// value for the style (a falsy value implies that the style
// is to be removed at the end of the animation). If we had a simple
// "OR" statement then it would not be enough to catch that.
function registerRestorableStyles(backup, node, properties) {
  forEach(properties, function(prop) {
    backup[prop] = isDefined(backup[prop])
        ? backup[prop]
        : node.style.getPropertyValue(prop);
  });
}

var $AnimateCssProvider = ['$animateProvider', /** @this */ function($animateProvider) {
  var gcsLookup = createLocalCacheLookup();
  var gcsStaggerLookup = createLocalCacheLookup();

  this.$get = ['$window', '$$jqLite', '$$AnimateRunner', '$timeout',
               '$$forceReflow', '$sniffer', '$$rAFScheduler', '$$animateQueue',
       function($window,   $$jqLite,   $$AnimateRunner,   $timeout,
                $$forceReflow,   $sniffer,   $$rAFScheduler, $$animateQueue) {

    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);

    var parentCounter = 0;
    function gcsHashFn(node, extraClasses) {
      var KEY = '$$ngAnimateParentKey';
      var parentNode = node.parentNode;
      var parentID = parentNode[KEY] || (parentNode[KEY] = ++parentCounter);
      return parentID + '-' + node.getAttribute('class') + '-' + extraClasses;
    }

    function computeCachedCssStyles(node, className, cacheKey, properties) {
      var timings = gcsLookup.get(cacheKey);

      if (!timings) {
        timings = computeCssStyles($window, node, properties);
        if (timings.animationIterationCount === 'infinite') {
          timings.animationIterationCount = 1;
        }
      }

      // we keep putting this in multiple times even though the value and the cacheKey are the same
      // because we're keeping an internal tally of how many duplicate animations are detected.
      gcsLookup.put(cacheKey, timings);
      return timings;
    }

    function computeCachedCssStaggerStyles(node, className, cacheKey, properties) {
      var stagger;

      // if we have one or more existing matches of matching elements
      // containing the same parent + CSS styles (which is how cacheKey works)
      // then staggering is possible
      if (gcsLookup.count(cacheKey) > 0) {
        stagger = gcsStaggerLookup.get(cacheKey);

        if (!stagger) {
          var staggerClassName = pendClasses(className, '-stagger');

          $$jqLite.addClass(node, staggerClassName);

          stagger = computeCssStyles($window, node, properties);

          // force the conversion of a null value to zero incase not set
          stagger.animationDuration = Math.max(stagger.animationDuration, 0);
          stagger.transitionDuration = Math.max(stagger.transitionDuration, 0);

          $$jqLite.removeClass(node, staggerClassName);

          gcsStaggerLookup.put(cacheKey, stagger);
        }
      }

      return stagger || {};
    }

    var rafWaitQueue = [];
    function waitUntilQuiet(callback) {
      rafWaitQueue.push(callback);
      $$rAFScheduler.waitUntilQuiet(function() {
        gcsLookup.flush();
        gcsStaggerLookup.flush();

        // DO NOT REMOVE THIS LINE OR REFACTOR OUT THE `pageWidth` variable.
        // PLEASE EXAMINE THE `$$forceReflow` service to understand why.
        var pageWidth = $$forceReflow();

        // we use a for loop to ensure that if the queue is changed
        // during this looping then it will consider new requests
        for (var i = 0; i < rafWaitQueue.length; i++) {
          rafWaitQueue[i](pageWidth);
        }
        rafWaitQueue.length = 0;
      });
    }

    function computeTimings(node, className, cacheKey) {
      var timings = computeCachedCssStyles(node, className, cacheKey, DETECT_CSS_PROPERTIES);
      var aD = timings.animationDelay;
      var tD = timings.transitionDelay;
      timings.maxDelay = aD && tD
          ? Math.max(aD, tD)
          : (aD || tD);
      timings.maxDuration = Math.max(
          timings.animationDuration * timings.animationIterationCount,
          timings.transitionDuration);

      return timings;
    }

    return function init(element, initialOptions) {
      // all of the animation functions should create
      // a copy of the options data, however, if a
      // parent service has already created a copy then
      // we should stick to using that
      var options = initialOptions || {};
      if (!options.$$prepared) {
        options = prepareAnimationOptions(copy(options));
      }

      var restoreStyles = {};
      var node = getDomNode(element);
      if (!node
          || !node.parentNode
          || !$$animateQueue.enabled()) {
        return closeAndReturnNoopAnimator();
      }

      var temporaryStyles = [];
      var classes = element.attr('class');
      var styles = packageStyles(options);
      var animationClosed;
      var animationPaused;
      var animationCompleted;
      var runner;
      var runnerHost;
      var maxDelay;
      var maxDelayTime;
      var maxDuration;
      var maxDurationTime;
      var startTime;
      var events = [];

      if (options.duration === 0 || (!$sniffer.animations && !$sniffer.transitions)) {
        return closeAndReturnNoopAnimator();
      }

      var method = options.event && isArray(options.event)
            ? options.event.join(' ')
            : options.event;

      var isStructural = method && options.structural;
      var structuralClassName = '';
      var addRemoveClassName = '';

      if (isStructural) {
        structuralClassName = pendClasses(method, EVENT_CLASS_PREFIX, true);
      } else if (method) {
        structuralClassName = method;
      }

      if (options.addClass) {
        addRemoveClassName += pendClasses(options.addClass, ADD_CLASS_SUFFIX);
      }

      if (options.removeClass) {
        if (addRemoveClassName.length) {
          addRemoveClassName += ' ';
        }
        addRemoveClassName += pendClasses(options.removeClass, REMOVE_CLASS_SUFFIX);
      }

      // there may be a situation where a structural animation is combined together
      // with CSS classes that need to resolve before the animation is computed.
      // However this means that there is no explicit CSS code to block the animation
      // from happening (by setting 0s none in the class name). If this is the case
      // we need to apply the classes before the first rAF so we know to continue if
      // there actually is a detected transition or keyframe animation
      if (options.applyClassesEarly && addRemoveClassName.length) {
        applyAnimationClasses(element, options);
      }

      var preparationClasses = [structuralClassName, addRemoveClassName].join(' ').trim();
      var fullClassName = classes + ' ' + preparationClasses;
      var activeClasses = pendClasses(preparationClasses, ACTIVE_CLASS_SUFFIX);
      var hasToStyles = styles.to && Object.keys(styles.to).length > 0;
      var containsKeyframeAnimation = (options.keyframeStyle || '').length > 0;

      // there is no way we can trigger an animation if no styles and
      // no classes are being applied which would then trigger a transition,
      // unless there a is raw keyframe value that is applied to the element.
      if (!containsKeyframeAnimation
           && !hasToStyles
           && !preparationClasses) {
        return closeAndReturnNoopAnimator();
      }

      var cacheKey, stagger;
      if (options.stagger > 0) {
        var staggerVal = parseFloat(options.stagger);
        stagger = {
          transitionDelay: staggerVal,
          animationDelay: staggerVal,
          transitionDuration: 0,
          animationDuration: 0
        };
      } else {
        cacheKey = gcsHashFn(node, fullClassName);
        stagger = computeCachedCssStaggerStyles(node, preparationClasses, cacheKey, DETECT_STAGGER_CSS_PROPERTIES);
      }

      if (!options.$$skipPreparationClasses) {
        $$jqLite.addClass(element, preparationClasses);
      }

      var applyOnlyDuration;

      if (options.transitionStyle) {
        var transitionStyle = [TRANSITION_PROP, options.transitionStyle];
        applyInlineStyle(node, transitionStyle);
        temporaryStyles.push(transitionStyle);
      }

      if (options.duration >= 0) {
        applyOnlyDuration = node.style[TRANSITION_PROP].length > 0;
        var durationStyle = getCssTransitionDurationStyle(options.duration, applyOnlyDuration);

        // we set the duration so that it will be picked up by getComputedStyle later
        applyInlineStyle(node, durationStyle);
        temporaryStyles.push(durationStyle);
      }

      if (options.keyframeStyle) {
        var keyframeStyle = [ANIMATION_PROP, options.keyframeStyle];
        applyInlineStyle(node, keyframeStyle);
        temporaryStyles.push(keyframeStyle);
      }

      var itemIndex = stagger
          ? options.staggerIndex >= 0
              ? options.staggerIndex
              : gcsLookup.count(cacheKey)
          : 0;

      var isFirst = itemIndex === 0;

      // this is a pre-emptive way of forcing the setup classes to be added and applied INSTANTLY
      // without causing any combination of transitions to kick in. By adding a negative delay value
      // it forces the setup class' transition to end immediately. We later then remove the negative
      // transition delay to allow for the transition to naturally do it's thing. The beauty here is
      // that if there is no transition defined then nothing will happen and this will also allow
      // other transitions to be stacked on top of each other without any chopping them out.
      if (isFirst && !options.skipBlocking) {
        blockTransitions(node, SAFE_FAST_FORWARD_DURATION_VALUE);
      }

      var timings = computeTimings(node, fullClassName, cacheKey);
      var relativeDelay = timings.maxDelay;
      maxDelay = Math.max(relativeDelay, 0);
      maxDuration = timings.maxDuration;

      var flags = {};
      flags.hasTransitions          = timings.transitionDuration > 0;
      flags.hasAnimations           = timings.animationDuration > 0;
      flags.hasTransitionAll        = flags.hasTransitions && timings.transitionProperty === 'all';
      flags.applyTransitionDuration = hasToStyles && (
                                        (flags.hasTransitions && !flags.hasTransitionAll)
                                         || (flags.hasAnimations && !flags.hasTransitions));
      flags.applyAnimationDuration  = options.duration && flags.hasAnimations;
      flags.applyTransitionDelay    = truthyTimingValue(options.delay) && (flags.applyTransitionDuration || flags.hasTransitions);
      flags.applyAnimationDelay     = truthyTimingValue(options.delay) && flags.hasAnimations;
      flags.recalculateTimingStyles = addRemoveClassName.length > 0;

      if (flags.applyTransitionDuration || flags.applyAnimationDuration) {
        maxDuration = options.duration ? parseFloat(options.duration) : maxDuration;

        if (flags.applyTransitionDuration) {
          flags.hasTransitions = true;
          timings.transitionDuration = maxDuration;
          applyOnlyDuration = node.style[TRANSITION_PROP + PROPERTY_KEY].length > 0;
          temporaryStyles.push(getCssTransitionDurationStyle(maxDuration, applyOnlyDuration));
        }

        if (flags.applyAnimationDuration) {
          flags.hasAnimations = true;
          timings.animationDuration = maxDuration;
          temporaryStyles.push(getCssKeyframeDurationStyle(maxDuration));
        }
      }

      if (maxDuration === 0 && !flags.recalculateTimingStyles) {
        return closeAndReturnNoopAnimator();
      }

      if (options.delay != null) {
        var delayStyle;
        if (typeof options.delay !== 'boolean') {
          delayStyle = parseFloat(options.delay);
          // number in options.delay means we have to recalculate the delay for the closing timeout
          maxDelay = Math.max(delayStyle, 0);
        }

        if (flags.applyTransitionDelay) {
          temporaryStyles.push(getCssDelayStyle(delayStyle));
        }

        if (flags.applyAnimationDelay) {
          temporaryStyles.push(getCssDelayStyle(delayStyle, true));
        }
      }

      // we need to recalculate the delay value since we used a pre-emptive negative
      // delay value and the delay value is required for the final event checking. This
      // property will ensure that this will happen after the RAF phase has passed.
      if (options.duration == null && timings.transitionDuration > 0) {
        flags.recalculateTimingStyles = flags.recalculateTimingStyles || isFirst;
      }

      maxDelayTime = maxDelay * ONE_SECOND;
      maxDurationTime = maxDuration * ONE_SECOND;
      if (!options.skipBlocking) {
        flags.blockTransition = timings.transitionDuration > 0;
        flags.blockKeyframeAnimation = timings.animationDuration > 0 &&
                                       stagger.animationDelay > 0 &&
                                       stagger.animationDuration === 0;
      }

      if (options.from) {
        if (options.cleanupStyles) {
          registerRestorableStyles(restoreStyles, node, Object.keys(options.from));
        }
        applyAnimationFromStyles(element, options);
      }

      if (flags.blockTransition || flags.blockKeyframeAnimation) {
        applyBlocking(maxDuration);
      } else if (!options.skipBlocking) {
        blockTransitions(node, false);
      }

      // TODO(matsko): for 1.5 change this code to have an animator object for better debugging
      return {
        $$willAnimate: true,
        end: endFn,
        start: function() {
          if (animationClosed) return;

          runnerHost = {
            end: endFn,
            cancel: cancelFn,
            resume: null, //this will be set during the start() phase
            pause: null
          };

          runner = new $$AnimateRunner(runnerHost);

          waitUntilQuiet(start);

          // we don't have access to pause/resume the animation
          // since it hasn't run yet. AnimateRunner will therefore
          // set noop functions for resume and pause and they will
          // later be overridden once the animation is triggered
          return runner;
        }
      };

      function endFn() {
        close();
      }

      function cancelFn() {
        close(true);
      }

      function close(rejected) {
        // if the promise has been called already then we shouldn't close
        // the animation again
        if (animationClosed || (animationCompleted && animationPaused)) return;
        animationClosed = true;
        animationPaused = false;

        if (!options.$$skipPreparationClasses) {
          $$jqLite.removeClass(element, preparationClasses);
        }
        $$jqLite.removeClass(element, activeClasses);

        blockKeyframeAnimations(node, false);
        blockTransitions(node, false);

        forEach(temporaryStyles, function(entry) {
          // There is only one way to remove inline style properties entirely from elements.
          // By using `removeProperty` this works, but we need to convert camel-cased CSS
          // styles down to hyphenated values.
          node.style[entry[0]] = '';
        });

        applyAnimationClasses(element, options);
        applyAnimationStyles(element, options);

        if (Object.keys(restoreStyles).length) {
          forEach(restoreStyles, function(value, prop) {
            if (value) {
              node.style.setProperty(prop, value);
            } else {
              node.style.removeProperty(prop);
            }
          });
        }

        // the reason why we have this option is to allow a synchronous closing callback
        // that is fired as SOON as the animation ends (when the CSS is removed) or if
        // the animation never takes off at all. A good example is a leave animation since
        // the element must be removed just after the animation is over or else the element
        // will appear on screen for one animation frame causing an overbearing flicker.
        if (options.onDone) {
          options.onDone();
        }

        if (events && events.length) {
          // Remove the transitionend / animationend listener(s)
          element.off(events.join(' '), onAnimationProgress);
        }

        //Cancel the fallback closing timeout and remove the timer data
        var animationTimerData = element.data(ANIMATE_TIMER_KEY);
        if (animationTimerData) {
          $timeout.cancel(animationTimerData[0].timer);
          element.removeData(ANIMATE_TIMER_KEY);
        }

        // if the preparation function fails then the promise is not setup
        if (runner) {
          runner.complete(!rejected);
        }
      }

      function applyBlocking(duration) {
        if (flags.blockTransition) {
          blockTransitions(node, duration);
        }

        if (flags.blockKeyframeAnimation) {
          blockKeyframeAnimations(node, !!duration);
        }
      }

      function closeAndReturnNoopAnimator() {
        runner = new $$AnimateRunner({
          end: endFn,
          cancel: cancelFn
        });

        // should flush the cache animation
        waitUntilQuiet(noop);
        close();

        return {
          $$willAnimate: false,
          start: function() {
            return runner;
          },
          end: endFn
        };
      }

      function onAnimationProgress(event) {
        event.stopPropagation();
        var ev = event.originalEvent || event;

        // we now always use `Date.now()` due to the recent changes with
        // event.timeStamp in Firefox, Webkit and Chrome (see #13494 for more info)
        var timeStamp = ev.$manualTimeStamp || Date.now();

        /* Firefox (or possibly just Gecko) likes to not round values up
         * when a ms measurement is used for the animation */
        var elapsedTime = parseFloat(ev.elapsedTime.toFixed(ELAPSED_TIME_MAX_DECIMAL_PLACES));

        /* $manualTimeStamp is a mocked timeStamp value which is set
         * within browserTrigger(). This is only here so that tests can
         * mock animations properly. Real events fallback to event.timeStamp,
         * or, if they don't, then a timeStamp is automatically created for them.
         * We're checking to see if the timeStamp surpasses the expected delay,
         * but we're using elapsedTime instead of the timeStamp on the 2nd
         * pre-condition since animationPauseds sometimes close off early */
        if (Math.max(timeStamp - startTime, 0) >= maxDelayTime && elapsedTime >= maxDuration) {
          // we set this flag to ensure that if the transition is paused then, when resumed,
          // the animation will automatically close itself since transitions cannot be paused.
          animationCompleted = true;
          close();
        }
      }

      function start() {
        if (animationClosed) return;
        if (!node.parentNode) {
          close();
          return;
        }

        // even though we only pause keyframe animations here the pause flag
        // will still happen when transitions are used. Only the transition will
        // not be paused since that is not possible. If the animation ends when
        // paused then it will not complete until unpaused or cancelled.
        var playPause = function(playAnimation) {
          if (!animationCompleted) {
            animationPaused = !playAnimation;
            if (timings.animationDuration) {
              var value = blockKeyframeAnimations(node, animationPaused);
              if (animationPaused) {
                temporaryStyles.push(value);
              } else {
                removeFromArray(temporaryStyles, value);
              }
            }
          } else if (animationPaused && playAnimation) {
            animationPaused = false;
            close();
          }
        };

        // checking the stagger duration prevents an accidentally cascade of the CSS delay style
        // being inherited from the parent. If the transition duration is zero then we can safely
        // rely that the delay value is an intentional stagger delay style.
        var maxStagger = itemIndex > 0
                         && ((timings.transitionDuration && stagger.transitionDuration === 0) ||
                            (timings.animationDuration && stagger.animationDuration === 0))
                         && Math.max(stagger.animationDelay, stagger.transitionDelay);
        if (maxStagger) {
          $timeout(triggerAnimationStart,
                   Math.floor(maxStagger * itemIndex * ONE_SECOND),
                   false);
        } else {
          triggerAnimationStart();
        }

        // this will decorate the existing promise runner with pause/resume methods
        runnerHost.resume = function() {
          playPause(true);
        };

        runnerHost.pause = function() {
          playPause(false);
        };

        function triggerAnimationStart() {
          // just incase a stagger animation kicks in when the animation
          // itself was cancelled entirely
          if (animationClosed) return;

          applyBlocking(false);

          forEach(temporaryStyles, function(entry) {
            var key = entry[0];
            var value = entry[1];
            node.style[key] = value;
          });

          applyAnimationClasses(element, options);
          $$jqLite.addClass(element, activeClasses);

          if (flags.recalculateTimingStyles) {
            fullClassName = node.getAttribute('class') + ' ' + preparationClasses;
            cacheKey = gcsHashFn(node, fullClassName);

            timings = computeTimings(node, fullClassName, cacheKey);
            relativeDelay = timings.maxDelay;
            maxDelay = Math.max(relativeDelay, 0);
            maxDuration = timings.maxDuration;

            if (maxDuration === 0) {
              close();
              return;
            }

            flags.hasTransitions = timings.transitionDuration > 0;
            flags.hasAnimations = timings.animationDuration > 0;
          }

          if (flags.applyAnimationDelay) {
            relativeDelay = typeof options.delay !== 'boolean' && truthyTimingValue(options.delay)
                  ? parseFloat(options.delay)
                  : relativeDelay;

            maxDelay = Math.max(relativeDelay, 0);
            timings.animationDelay = relativeDelay;
            delayStyle = getCssDelayStyle(relativeDelay, true);
            temporaryStyles.push(delayStyle);
            node.style[delayStyle[0]] = delayStyle[1];
          }

          maxDelayTime = maxDelay * ONE_SECOND;
          maxDurationTime = maxDuration * ONE_SECOND;

          if (options.easing) {
            var easeProp, easeVal = options.easing;
            if (flags.hasTransitions) {
              easeProp = TRANSITION_PROP + TIMING_KEY;
              temporaryStyles.push([easeProp, easeVal]);
              node.style[easeProp] = easeVal;
            }
            if (flags.hasAnimations) {
              easeProp = ANIMATION_PROP + TIMING_KEY;
              temporaryStyles.push([easeProp, easeVal]);
              node.style[easeProp] = easeVal;
            }
          }

          if (timings.transitionDuration) {
            events.push(TRANSITIONEND_EVENT);
          }

          if (timings.animationDuration) {
            events.push(ANIMATIONEND_EVENT);
          }

          startTime = Date.now();
          var timerTime = maxDelayTime + CLOSING_TIME_BUFFER * maxDurationTime;
          var endTime = startTime + timerTime;

          var animationsData = element.data(ANIMATE_TIMER_KEY) || [];
          var setupFallbackTimer = true;
          if (animationsData.length) {
            var currentTimerData = animationsData[0];
            setupFallbackTimer = endTime > currentTimerData.expectedEndTime;
            if (setupFallbackTimer) {
              $timeout.cancel(currentTimerData.timer);
            } else {
              animationsData.push(close);
            }
          }

          if (setupFallbackTimer) {
            var timer = $timeout(onAnimationExpired, timerTime, false);
            animationsData[0] = {
              timer: timer,
              expectedEndTime: endTime
            };
            animationsData.push(close);
            element.data(ANIMATE_TIMER_KEY, animationsData);
          }

          if (events.length) {
            element.on(events.join(' '), onAnimationProgress);
          }

          if (options.to) {
            if (options.cleanupStyles) {
              registerRestorableStyles(restoreStyles, node, Object.keys(options.to));
            }
            applyAnimationToStyles(element, options);
          }
        }

        function onAnimationExpired() {
          var animationsData = element.data(ANIMATE_TIMER_KEY);

          // this will be false in the event that the element was
          // removed from the DOM (via a leave animation or something
          // similar)
          if (animationsData) {
            for (var i = 1; i < animationsData.length; i++) {
              animationsData[i]();
            }
            element.removeData(ANIMATE_TIMER_KEY);
          }
        }
      }
    };
  }];
}];

var $$AnimateCssDriverProvider = ['$$animationProvider', /** @this */ function($$animationProvider) {
  $$animationProvider.drivers.push('$$animateCssDriver');

  var NG_ANIMATE_SHIM_CLASS_NAME = 'ng-animate-shim';
  var NG_ANIMATE_ANCHOR_CLASS_NAME = 'ng-anchor';

  var NG_OUT_ANCHOR_CLASS_NAME = 'ng-anchor-out';
  var NG_IN_ANCHOR_CLASS_NAME = 'ng-anchor-in';

  function isDocumentFragment(node) {
    return node.parentNode && node.parentNode.nodeType === 11;
  }

  this.$get = ['$animateCss', '$rootScope', '$$AnimateRunner', '$rootElement', '$sniffer', '$$jqLite', '$document',
       function($animateCss,   $rootScope,   $$AnimateRunner,   $rootElement,   $sniffer,   $$jqLite,   $document) {

    // only browsers that support these properties can render animations
    if (!$sniffer.animations && !$sniffer.transitions) return noop;

    var bodyNode = $document[0].body;
    var rootNode = getDomNode($rootElement);

    var rootBodyElement = jqLite(
      // this is to avoid using something that exists outside of the body
      // we also special case the doc fragment case because our unit test code
      // appends the $rootElement to the body after the app has been bootstrapped
      isDocumentFragment(rootNode) || bodyNode.contains(rootNode) ? rootNode : bodyNode
    );

    return function initDriverFn(animationDetails) {
      return animationDetails.from && animationDetails.to
          ? prepareFromToAnchorAnimation(animationDetails.from,
                                         animationDetails.to,
                                         animationDetails.classes,
                                         animationDetails.anchors)
          : prepareRegularAnimation(animationDetails);
    };

    function filterCssClasses(classes) {
      //remove all the `ng-` stuff
      return classes.replace(/\bng-\S+\b/g, '');
    }

    function getUniqueValues(a, b) {
      if (isString(a)) a = a.split(' ');
      if (isString(b)) b = b.split(' ');
      return a.filter(function(val) {
        return b.indexOf(val) === -1;
      }).join(' ');
    }

    function prepareAnchoredAnimation(classes, outAnchor, inAnchor) {
      var clone = jqLite(getDomNode(outAnchor).cloneNode(true));
      var startingClasses = filterCssClasses(getClassVal(clone));

      outAnchor.addClass(NG_ANIMATE_SHIM_CLASS_NAME);
      inAnchor.addClass(NG_ANIMATE_SHIM_CLASS_NAME);

      clone.addClass(NG_ANIMATE_ANCHOR_CLASS_NAME);

      rootBodyElement.append(clone);

      var animatorIn, animatorOut = prepareOutAnimation();

      // the user may not end up using the `out` animation and
      // only making use of the `in` animation or vice-versa.
      // In either case we should allow this and not assume the
      // animation is over unless both animations are not used.
      if (!animatorOut) {
        animatorIn = prepareInAnimation();
        if (!animatorIn) {
          return end();
        }
      }

      var startingAnimator = animatorOut || animatorIn;

      return {
        start: function() {
          var runner;

          var currentAnimation = startingAnimator.start();
          currentAnimation.done(function() {
            currentAnimation = null;
            if (!animatorIn) {
              animatorIn = prepareInAnimation();
              if (animatorIn) {
                currentAnimation = animatorIn.start();
                currentAnimation.done(function() {
                  currentAnimation = null;
                  end();
                  runner.complete();
                });
                return currentAnimation;
              }
            }
            // in the event that there is no `in` animation
            end();
            runner.complete();
          });

          runner = new $$AnimateRunner({
            end: endFn,
            cancel: endFn
          });

          return runner;

          function endFn() {
            if (currentAnimation) {
              currentAnimation.end();
            }
          }
        }
      };

      function calculateAnchorStyles(anchor) {
        var styles = {};

        var coords = getDomNode(anchor).getBoundingClientRect();

        // we iterate directly since safari messes up and doesn't return
        // all the keys for the coords object when iterated
        forEach(['width','height','top','left'], function(key) {
          var value = coords[key];
          switch (key) {
            case 'top':
              value += bodyNode.scrollTop;
              break;
            case 'left':
              value += bodyNode.scrollLeft;
              break;
          }
          styles[key] = Math.floor(value) + 'px';
        });
        return styles;
      }

      function prepareOutAnimation() {
        var animator = $animateCss(clone, {
          addClass: NG_OUT_ANCHOR_CLASS_NAME,
          delay: true,
          from: calculateAnchorStyles(outAnchor)
        });

        // read the comment within `prepareRegularAnimation` to understand
        // why this check is necessary
        return animator.$$willAnimate ? animator : null;
      }

      function getClassVal(element) {
        return element.attr('class') || '';
      }

      function prepareInAnimation() {
        var endingClasses = filterCssClasses(getClassVal(inAnchor));
        var toAdd = getUniqueValues(endingClasses, startingClasses);
        var toRemove = getUniqueValues(startingClasses, endingClasses);

        var animator = $animateCss(clone, {
          to: calculateAnchorStyles(inAnchor),
          addClass: NG_IN_ANCHOR_CLASS_NAME + ' ' + toAdd,
          removeClass: NG_OUT_ANCHOR_CLASS_NAME + ' ' + toRemove,
          delay: true
        });

        // read the comment within `prepareRegularAnimation` to understand
        // why this check is necessary
        return animator.$$willAnimate ? animator : null;
      }

      function end() {
        clone.remove();
        outAnchor.removeClass(NG_ANIMATE_SHIM_CLASS_NAME);
        inAnchor.removeClass(NG_ANIMATE_SHIM_CLASS_NAME);
      }
    }

    function prepareFromToAnchorAnimation(from, to, classes, anchors) {
      var fromAnimation = prepareRegularAnimation(from, noop);
      var toAnimation = prepareRegularAnimation(to, noop);

      var anchorAnimations = [];
      forEach(anchors, function(anchor) {
        var outElement = anchor['out'];
        var inElement = anchor['in'];
        var animator = prepareAnchoredAnimation(classes, outElement, inElement);
        if (animator) {
          anchorAnimations.push(animator);
        }
      });

      // no point in doing anything when there are no elements to animate
      if (!fromAnimation && !toAnimation && anchorAnimations.length === 0) return;

      return {
        start: function() {
          var animationRunners = [];

          if (fromAnimation) {
            animationRunners.push(fromAnimation.start());
          }

          if (toAnimation) {
            animationRunners.push(toAnimation.start());
          }

          forEach(anchorAnimations, function(animation) {
            animationRunners.push(animation.start());
          });

          var runner = new $$AnimateRunner({
            end: endFn,
            cancel: endFn // CSS-driven animations cannot be cancelled, only ended
          });

          $$AnimateRunner.all(animationRunners, function(status) {
            runner.complete(status);
          });

          return runner;

          function endFn() {
            forEach(animationRunners, function(runner) {
              runner.end();
            });
          }
        }
      };
    }

    function prepareRegularAnimation(animationDetails) {
      var element = animationDetails.element;
      var options = animationDetails.options || {};

      if (animationDetails.structural) {
        options.event = animationDetails.event;
        options.structural = true;
        options.applyClassesEarly = true;

        // we special case the leave animation since we want to ensure that
        // the element is removed as soon as the animation is over. Otherwise
        // a flicker might appear or the element may not be removed at all
        if (animationDetails.event === 'leave') {
          options.onDone = options.domOperation;
        }
      }

      // We assign the preparationClasses as the actual animation event since
      // the internals of $animateCss will just suffix the event token values
      // with `-active` to trigger the animation.
      if (options.preparationClasses) {
        options.event = concatWithSpace(options.event, options.preparationClasses);
      }

      var animator = $animateCss(element, options);

      // the driver lookup code inside of $$animation attempts to spawn a
      // driver one by one until a driver returns a.$$willAnimate animator object.
      // $animateCss will always return an object, however, it will pass in
      // a flag as a hint as to whether an animation was detected or not
      return animator.$$willAnimate ? animator : null;
    }
  }];
}];

// TODO(matsko): use caching here to speed things up for detection
// TODO(matsko): add documentation
//  by the time...

var $$AnimateJsProvider = ['$animateProvider', /** @this */ function($animateProvider) {
  this.$get = ['$injector', '$$AnimateRunner', '$$jqLite',
       function($injector,   $$AnimateRunner,   $$jqLite) {

    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);
         // $animateJs(element, 'enter');
    return function(element, event, classes, options) {
      var animationClosed = false;

      // the `classes` argument is optional and if it is not used
      // then the classes will be resolved from the element's className
      // property as well as options.addClass/options.removeClass.
      if (arguments.length === 3 && isObject(classes)) {
        options = classes;
        classes = null;
      }

      options = prepareAnimationOptions(options);
      if (!classes) {
        classes = element.attr('class') || '';
        if (options.addClass) {
          classes += ' ' + options.addClass;
        }
        if (options.removeClass) {
          classes += ' ' + options.removeClass;
        }
      }

      var classesToAdd = options.addClass;
      var classesToRemove = options.removeClass;

      // the lookupAnimations function returns a series of animation objects that are
      // matched up with one or more of the CSS classes. These animation objects are
      // defined via the module.animation factory function. If nothing is detected then
      // we don't return anything which then makes $animation query the next driver.
      var animations = lookupAnimations(classes);
      var before, after;
      if (animations.length) {
        var afterFn, beforeFn;
        if (event === 'leave') {
          beforeFn = 'leave';
          afterFn = 'afterLeave'; // TODO(matsko): get rid of this
        } else {
          beforeFn = 'before' + event.charAt(0).toUpperCase() + event.substr(1);
          afterFn = event;
        }

        if (event !== 'enter' && event !== 'move') {
          before = packageAnimations(element, event, options, animations, beforeFn);
        }
        after  = packageAnimations(element, event, options, animations, afterFn);
      }

      // no matching animations
      if (!before && !after) return;

      function applyOptions() {
        options.domOperation();
        applyAnimationClasses(element, options);
      }

      function close() {
        animationClosed = true;
        applyOptions();
        applyAnimationStyles(element, options);
      }

      var runner;

      return {
        $$willAnimate: true,
        end: function() {
          if (runner) {
            runner.end();
          } else {
            close();
            runner = new $$AnimateRunner();
            runner.complete(true);
          }
          return runner;
        },
        start: function() {
          if (runner) {
            return runner;
          }

          runner = new $$AnimateRunner();
          var closeActiveAnimations;
          var chain = [];

          if (before) {
            chain.push(function(fn) {
              closeActiveAnimations = before(fn);
            });
          }

          if (chain.length) {
            chain.push(function(fn) {
              applyOptions();
              fn(true);
            });
          } else {
            applyOptions();
          }

          if (after) {
            chain.push(function(fn) {
              closeActiveAnimations = after(fn);
            });
          }

          runner.setHost({
            end: function() {
              endAnimations();
            },
            cancel: function() {
              endAnimations(true);
            }
          });

          $$AnimateRunner.chain(chain, onComplete);
          return runner;

          function onComplete(success) {
            close(success);
            runner.complete(success);
          }

          function endAnimations(cancelled) {
            if (!animationClosed) {
              (closeActiveAnimations || noop)(cancelled);
              onComplete(cancelled);
            }
          }
        }
      };

      function executeAnimationFn(fn, element, event, options, onDone) {
        var args;
        switch (event) {
          case 'animate':
            args = [element, options.from, options.to, onDone];
            break;

          case 'setClass':
            args = [element, classesToAdd, classesToRemove, onDone];
            break;

          case 'addClass':
            args = [element, classesToAdd, onDone];
            break;

          case 'removeClass':
            args = [element, classesToRemove, onDone];
            break;

          default:
            args = [element, onDone];
            break;
        }

        args.push(options);

        var value = fn.apply(fn, args);
        if (value) {
          if (isFunction(value.start)) {
            value = value.start();
          }

          if (value instanceof $$AnimateRunner) {
            value.done(onDone);
          } else if (isFunction(value)) {
            // optional onEnd / onCancel callback
            return value;
          }
        }

        return noop;
      }

      function groupEventedAnimations(element, event, options, animations, fnName) {
        var operations = [];
        forEach(animations, function(ani) {
          var animation = ani[fnName];
          if (!animation) return;

          // note that all of these animations will run in parallel
          operations.push(function() {
            var runner;
            var endProgressCb;

            var resolved = false;
            var onAnimationComplete = function(rejected) {
              if (!resolved) {
                resolved = true;
                (endProgressCb || noop)(rejected);
                runner.complete(!rejected);
              }
            };

            runner = new $$AnimateRunner({
              end: function() {
                onAnimationComplete();
              },
              cancel: function() {
                onAnimationComplete(true);
              }
            });

            endProgressCb = executeAnimationFn(animation, element, event, options, function(result) {
              var cancelled = result === false;
              onAnimationComplete(cancelled);
            });

            return runner;
          });
        });

        return operations;
      }

      function packageAnimations(element, event, options, animations, fnName) {
        var operations = groupEventedAnimations(element, event, options, animations, fnName);
        if (operations.length === 0) {
          var a, b;
          if (fnName === 'beforeSetClass') {
            a = groupEventedAnimations(element, 'removeClass', options, animations, 'beforeRemoveClass');
            b = groupEventedAnimations(element, 'addClass', options, animations, 'beforeAddClass');
          } else if (fnName === 'setClass') {
            a = groupEventedAnimations(element, 'removeClass', options, animations, 'removeClass');
            b = groupEventedAnimations(element, 'addClass', options, animations, 'addClass');
          }

          if (a) {
            operations = operations.concat(a);
          }
          if (b) {
            operations = operations.concat(b);
          }
        }

        if (operations.length === 0) return;

        // TODO(matsko): add documentation
        return function startAnimation(callback) {
          var runners = [];
          if (operations.length) {
            forEach(operations, function(animateFn) {
              runners.push(animateFn());
            });
          }

          if (runners.length) {
            $$AnimateRunner.all(runners, callback);
          }  else {
            callback();
          }

          return function endFn(reject) {
            forEach(runners, function(runner) {
              if (reject) {
                runner.cancel();
              } else {
                runner.end();
              }
            });
          };
        };
      }
    };

    function lookupAnimations(classes) {
      classes = isArray(classes) ? classes : classes.split(' ');
      var matches = [], flagMap = {};
      for (var i = 0; i < classes.length; i++) {
        var klass = classes[i],
            animationFactory = $animateProvider.$$registeredAnimations[klass];
        if (animationFactory && !flagMap[klass]) {
          matches.push($injector.get(animationFactory));
          flagMap[klass] = true;
        }
      }
      return matches;
    }
  }];
}];

var $$AnimateJsDriverProvider = ['$$animationProvider', /** @this */ function($$animationProvider) {
  $$animationProvider.drivers.push('$$animateJsDriver');
  this.$get = ['$$animateJs', '$$AnimateRunner', function($$animateJs, $$AnimateRunner) {
    return function initDriverFn(animationDetails) {
      if (animationDetails.from && animationDetails.to) {
        var fromAnimation = prepareAnimation(animationDetails.from);
        var toAnimation = prepareAnimation(animationDetails.to);
        if (!fromAnimation && !toAnimation) return;

        return {
          start: function() {
            var animationRunners = [];

            if (fromAnimation) {
              animationRunners.push(fromAnimation.start());
            }

            if (toAnimation) {
              animationRunners.push(toAnimation.start());
            }

            $$AnimateRunner.all(animationRunners, done);

            var runner = new $$AnimateRunner({
              end: endFnFactory(),
              cancel: endFnFactory()
            });

            return runner;

            function endFnFactory() {
              return function() {
                forEach(animationRunners, function(runner) {
                  // at this point we cannot cancel animations for groups just yet. 1.5+
                  runner.end();
                });
              };
            }

            function done(status) {
              runner.complete(status);
            }
          }
        };
      } else {
        return prepareAnimation(animationDetails);
      }
    };

    function prepareAnimation(animationDetails) {
      // TODO(matsko): make sure to check for grouped animations and delegate down to normal animations
      var element = animationDetails.element;
      var event = animationDetails.event;
      var options = animationDetails.options;
      var classes = animationDetails.classes;
      return $$animateJs(element, event, classes, options);
    }
  }];
}];

var NG_ANIMATE_ATTR_NAME = 'data-ng-animate';
var NG_ANIMATE_PIN_DATA = '$ngAnimatePin';
var $$AnimateQueueProvider = ['$animateProvider', /** @this */ function($animateProvider) {
  var PRE_DIGEST_STATE = 1;
  var RUNNING_STATE = 2;
  var ONE_SPACE = ' ';

  var rules = this.rules = {
    skip: [],
    cancel: [],
    join: []
  };

  function makeTruthyCssClassMap(classString) {
    if (!classString) {
      return null;
    }

    var keys = classString.split(ONE_SPACE);
    var map = Object.create(null);

    forEach(keys, function(key) {
      map[key] = true;
    });
    return map;
  }

  function hasMatchingClasses(newClassString, currentClassString) {
    if (newClassString && currentClassString) {
      var currentClassMap = makeTruthyCssClassMap(currentClassString);
      return newClassString.split(ONE_SPACE).some(function(className) {
        return currentClassMap[className];
      });
    }
  }

  function isAllowed(ruleType, currentAnimation, previousAnimation) {
    return rules[ruleType].some(function(fn) {
      return fn(currentAnimation, previousAnimation);
    });
  }

  function hasAnimationClasses(animation, and) {
    var a = (animation.addClass || '').length > 0;
    var b = (animation.removeClass || '').length > 0;
    return and ? a && b : a || b;
  }

  rules.join.push(function(newAnimation, currentAnimation) {
    // if the new animation is class-based then we can just tack that on
    return !newAnimation.structural && hasAnimationClasses(newAnimation);
  });

  rules.skip.push(function(newAnimation, currentAnimation) {
    // there is no need to animate anything if no classes are being added and
    // there is no structural animation that will be triggered
    return !newAnimation.structural && !hasAnimationClasses(newAnimation);
  });

  rules.skip.push(function(newAnimation, currentAnimation) {
    // why should we trigger a new structural animation if the element will
    // be removed from the DOM anyway?
    return currentAnimation.event === 'leave' && newAnimation.structural;
  });

  rules.skip.push(function(newAnimation, currentAnimation) {
    // if there is an ongoing current animation then don't even bother running the class-based animation
    return currentAnimation.structural && currentAnimation.state === RUNNING_STATE && !newAnimation.structural;
  });

  rules.cancel.push(function(newAnimation, currentAnimation) {
    // there can never be two structural animations running at the same time
    return currentAnimation.structural && newAnimation.structural;
  });

  rules.cancel.push(function(newAnimation, currentAnimation) {
    // if the previous animation is already running, but the new animation will
    // be triggered, but the new animation is structural
    return currentAnimation.state === RUNNING_STATE && newAnimation.structural;
  });

  rules.cancel.push(function(newAnimation, currentAnimation) {
    // cancel the animation if classes added / removed in both animation cancel each other out,
    // but only if the current animation isn't structural

    if (currentAnimation.structural) return false;

    var nA = newAnimation.addClass;
    var nR = newAnimation.removeClass;
    var cA = currentAnimation.addClass;
    var cR = currentAnimation.removeClass;

    // early detection to save the global CPU shortage :)
    if ((isUndefined(nA) && isUndefined(nR)) || (isUndefined(cA) && isUndefined(cR))) {
      return false;
    }

    return hasMatchingClasses(nA, cR) || hasMatchingClasses(nR, cA);
  });

  this.$get = ['$$rAF', '$rootScope', '$rootElement', '$document', '$$Map',
               '$$animation', '$$AnimateRunner', '$templateRequest', '$$jqLite', '$$forceReflow',
               '$$isDocumentHidden',
       function($$rAF,   $rootScope,   $rootElement,   $document,   $$Map,
                $$animation,   $$AnimateRunner,   $templateRequest,   $$jqLite,   $$forceReflow,
                $$isDocumentHidden) {

    var activeAnimationsLookup = new $$Map();
    var disabledElementsLookup = new $$Map();
    var animationsEnabled = null;

    function postDigestTaskFactory() {
      var postDigestCalled = false;
      return function(fn) {
        // we only issue a call to postDigest before
        // it has first passed. This prevents any callbacks
        // from not firing once the animation has completed
        // since it will be out of the digest cycle.
        if (postDigestCalled) {
          fn();
        } else {
          $rootScope.$$postDigest(function() {
            postDigestCalled = true;
            fn();
          });
        }
      };
    }

    // Wait until all directive and route-related templates are downloaded and
    // compiled. The $templateRequest.totalPendingRequests variable keeps track of
    // all of the remote templates being currently downloaded. If there are no
    // templates currently downloading then the watcher will still fire anyway.
    var deregisterWatch = $rootScope.$watch(
      function() { return $templateRequest.totalPendingRequests === 0; },
      function(isEmpty) {
        if (!isEmpty) return;
        deregisterWatch();

        // Now that all templates have been downloaded, $animate will wait until
        // the post digest queue is empty before enabling animations. By having two
        // calls to $postDigest calls we can ensure that the flag is enabled at the
        // very end of the post digest queue. Since all of the animations in $animate
        // use $postDigest, it's important that the code below executes at the end.
        // This basically means that the page is fully downloaded and compiled before
        // any animations are triggered.
        $rootScope.$$postDigest(function() {
          $rootScope.$$postDigest(function() {
            // we check for null directly in the event that the application already called
            // .enabled() with whatever arguments that it provided it with
            if (animationsEnabled === null) {
              animationsEnabled = true;
            }
          });
        });
      }
    );

    var callbackRegistry = Object.create(null);

    // remember that the classNameFilter is set during the provider/config
    // stage therefore we can optimize here and setup a helper function
    var classNameFilter = $animateProvider.classNameFilter();
    var isAnimatableClassName = !classNameFilter
              ? function() { return true; }
              : function(className) {
                return classNameFilter.test(className);
              };

    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);

    function normalizeAnimationDetails(element, animation) {
      return mergeAnimationDetails(element, animation, {});
    }

    // IE9-11 has no method "contains" in SVG element and in Node.prototype. Bug #10259.
    var contains = window.Node.prototype.contains || /** @this */ function(arg) {
      // eslint-disable-next-line no-bitwise
      return this === arg || !!(this.compareDocumentPosition(arg) & 16);
    };

    function findCallbacks(targetParentNode, targetNode, event) {
      var matches = [];
      var entries = callbackRegistry[event];
      if (entries) {
        forEach(entries, function(entry) {
          if (contains.call(entry.node, targetNode)) {
            matches.push(entry.callback);
          } else if (event === 'leave' && contains.call(entry.node, targetParentNode)) {
            matches.push(entry.callback);
          }
        });
      }

      return matches;
    }

    function filterFromRegistry(list, matchContainer, matchCallback) {
      var containerNode = extractElementNode(matchContainer);
      return list.filter(function(entry) {
        var isMatch = entry.node === containerNode &&
                        (!matchCallback || entry.callback === matchCallback);
        return !isMatch;
      });
    }

    function cleanupEventListeners(phase, node) {
      if (phase === 'close' && !node.parentNode) {
        // If the element is not attached to a parentNode, it has been removed by
        // the domOperation, and we can safely remove the event callbacks
        $animate.off(node);
      }
    }

    var $animate = {
      on: function(event, container, callback) {
        var node = extractElementNode(container);
        callbackRegistry[event] = callbackRegistry[event] || [];
        callbackRegistry[event].push({
          node: node,
          callback: callback
        });

        // Remove the callback when the element is removed from the DOM
        jqLite(container).on('$destroy', function() {
          var animationDetails = activeAnimationsLookup.get(node);

          if (!animationDetails) {
            // If there's an animation ongoing, the callback calling code will remove
            // the event listeners. If we'd remove here, the callbacks would be removed
            // before the animation ends
            $animate.off(event, container, callback);
          }
        });
      },

      off: function(event, container, callback) {
        if (arguments.length === 1 && !isString(arguments[0])) {
          container = arguments[0];
          for (var eventType in callbackRegistry) {
            callbackRegistry[eventType] = filterFromRegistry(callbackRegistry[eventType], container);
          }

          return;
        }

        var entries = callbackRegistry[event];
        if (!entries) return;

        callbackRegistry[event] = arguments.length === 1
            ? null
            : filterFromRegistry(entries, container, callback);
      },

      pin: function(element, parentElement) {
        assertArg(isElement(element), 'element', 'not an element');
        assertArg(isElement(parentElement), 'parentElement', 'not an element');
        element.data(NG_ANIMATE_PIN_DATA, parentElement);
      },

      push: function(element, event, options, domOperation) {
        options = options || {};
        options.domOperation = domOperation;
        return queueAnimation(element, event, options);
      },

      // this method has four signatures:
      //  () - global getter
      //  (bool) - global setter
      //  (element) - element getter
      //  (element, bool) - element setter<F37>
      enabled: function(element, bool) {
        var argCount = arguments.length;

        if (argCount === 0) {
          // () - Global getter
          bool = !!animationsEnabled;
        } else {
          var hasElement = isElement(element);

          if (!hasElement) {
            // (bool) - Global setter
            bool = animationsEnabled = !!element;
          } else {
            var node = getDomNode(element);

            if (argCount === 1) {
              // (element) - Element getter
              bool = !disabledElementsLookup.get(node);
            } else {
              // (element, bool) - Element setter
              disabledElementsLookup.set(node, !bool);
            }
          }
        }

        return bool;
      }
    };

    return $animate;

    function queueAnimation(originalElement, event, initialOptions) {
      // we always make a copy of the options since
      // there should never be any side effects on
      // the input data when running `$animateCss`.
      var options = copy(initialOptions);

      var element = stripCommentsFromElement(originalElement);
      var node = getDomNode(element);
      var parentNode = node && node.parentNode;

      options = prepareAnimationOptions(options);

      // we create a fake runner with a working promise.
      // These methods will become available after the digest has passed
      var runner = new $$AnimateRunner();

      // this is used to trigger callbacks in postDigest mode
      var runInNextPostDigestOrNow = postDigestTaskFactory();

      if (isArray(options.addClass)) {
        options.addClass = options.addClass.join(' ');
      }

      if (options.addClass && !isString(options.addClass)) {
        options.addClass = null;
      }

      if (isArray(options.removeClass)) {
        options.removeClass = options.removeClass.join(' ');
      }

      if (options.removeClass && !isString(options.removeClass)) {
        options.removeClass = null;
      }

      if (options.from && !isObject(options.from)) {
        options.from = null;
      }

      if (options.to && !isObject(options.to)) {
        options.to = null;
      }

      // there are situations where a directive issues an animation for
      // a jqLite wrapper that contains only comment nodes... If this
      // happens then there is no way we can perform an animation
      if (!node) {
        close();
        return runner;
      }

      var className = [node.getAttribute('class'), options.addClass, options.removeClass].join(' ');
      if (!isAnimatableClassName(className)) {
        close();
        return runner;
      }

      var isStructural = ['enter', 'move', 'leave'].indexOf(event) >= 0;

      var documentHidden = $$isDocumentHidden();

      // this is a hard disable of all animations for the application or on
      // the element itself, therefore  there is no need to continue further
      // past this point if not enabled
      // Animations are also disabled if the document is currently hidden (page is not visible
      // to the user), because browsers slow down or do not flush calls to requestAnimationFrame
      var skipAnimations = !animationsEnabled || documentHidden || disabledElementsLookup.get(node);
      var existingAnimation = (!skipAnimations && activeAnimationsLookup.get(node)) || {};
      var hasExistingAnimation = !!existingAnimation.state;

      // there is no point in traversing the same collection of parent ancestors if a followup
      // animation will be run on the same element that already did all that checking work
      if (!skipAnimations && (!hasExistingAnimation || existingAnimation.state !== PRE_DIGEST_STATE)) {
        skipAnimations = !areAnimationsAllowed(node, parentNode, event);
      }

      if (skipAnimations) {
        // Callbacks should fire even if the document is hidden (regression fix for issue #14120)
        if (documentHidden) notifyProgress(runner, event, 'start');
        close();
        if (documentHidden) notifyProgress(runner, event, 'close');
        return runner;
      }

      if (isStructural) {
        closeChildAnimations(node);
      }

      var newAnimation = {
        structural: isStructural,
        element: element,
        event: event,
        addClass: options.addClass,
        removeClass: options.removeClass,
        close: close,
        options: options,
        runner: runner
      };

      if (hasExistingAnimation) {
        var skipAnimationFlag = isAllowed('skip', newAnimation, existingAnimation);
        if (skipAnimationFlag) {
          if (existingAnimation.state === RUNNING_STATE) {
            close();
            return runner;
          } else {
            mergeAnimationDetails(element, existingAnimation, newAnimation);
            return existingAnimation.runner;
          }
        }
        var cancelAnimationFlag = isAllowed('cancel', newAnimation, existingAnimation);
        if (cancelAnimationFlag) {
          if (existingAnimation.state === RUNNING_STATE) {
            // this will end the animation right away and it is safe
            // to do so since the animation is already running and the
            // runner callback code will run in async
            existingAnimation.runner.end();
          } else if (existingAnimation.structural) {
            // this means that the animation is queued into a digest, but
            // hasn't started yet. Therefore it is safe to run the close
            // method which will call the runner methods in async.
            existingAnimation.close();
          } else {
            // this will merge the new animation options into existing animation options
            mergeAnimationDetails(element, existingAnimation, newAnimation);

            return existingAnimation.runner;
          }
        } else {
          // a joined animation means that this animation will take over the existing one
          // so an example would involve a leave animation taking over an enter. Then when
          // the postDigest kicks in the enter will be ignored.
          var joinAnimationFlag = isAllowed('join', newAnimation, existingAnimation);
          if (joinAnimationFlag) {
            if (existingAnimation.state === RUNNING_STATE) {
              normalizeAnimationDetails(element, newAnimation);
            } else {
              applyGeneratedPreparationClasses(element, isStructural ? event : null, options);

              event = newAnimation.event = existingAnimation.event;
              options = mergeAnimationDetails(element, existingAnimation, newAnimation);

              //we return the same runner since only the option values of this animation will
              //be fed into the `existingAnimation`.
              return existingAnimation.runner;
            }
          }
        }
      } else {
        // normalization in this case means that it removes redundant CSS classes that
        // already exist (addClass) or do not exist (removeClass) on the element
        normalizeAnimationDetails(element, newAnimation);
      }

      // when the options are merged and cleaned up we may end up not having to do
      // an animation at all, therefore we should check this before issuing a post
      // digest callback. Structural animations will always run no matter what.
      var isValidAnimation = newAnimation.structural;
      if (!isValidAnimation) {
        // animate (from/to) can be quickly checked first, otherwise we check if any classes are present
        isValidAnimation = (newAnimation.event === 'animate' && Object.keys(newAnimation.options.to || {}).length > 0)
                            || hasAnimationClasses(newAnimation);
      }

      if (!isValidAnimation) {
        close();
        clearElementAnimationState(node);
        return runner;
      }

      // the counter keeps track of cancelled animations
      var counter = (existingAnimation.counter || 0) + 1;
      newAnimation.counter = counter;

      markElementAnimationState(node, PRE_DIGEST_STATE, newAnimation);

      $rootScope.$$postDigest(function() {
        // It is possible that the DOM nodes inside `originalElement` have been replaced. This can
        // happen if the animated element is a transcluded clone and also has a `templateUrl`
        // directive on it. Therefore, we must recreate `element` in order to interact with the
        // actual DOM nodes.
        // Note: We still need to use the old `node` for certain things, such as looking up in
        //       HashMaps where it was used as the key.

        element = stripCommentsFromElement(originalElement);

        var animationDetails = activeAnimationsLookup.get(node);
        var animationCancelled = !animationDetails;
        animationDetails = animationDetails || {};

        // if addClass/removeClass is called before something like enter then the
        // registered parent element may not be present. The code below will ensure
        // that a final value for parent element is obtained
        var parentElement = element.parent() || [];

        // animate/structural/class-based animations all have requirements. Otherwise there
        // is no point in performing an animation. The parent node must also be set.
        var isValidAnimation = parentElement.length > 0
                                && (animationDetails.event === 'animate'
                                    || animationDetails.structural
                                    || hasAnimationClasses(animationDetails));

        // this means that the previous animation was cancelled
        // even if the follow-up animation is the same event
        if (animationCancelled || animationDetails.counter !== counter || !isValidAnimation) {
          // if another animation did not take over then we need
          // to make sure that the domOperation and options are
          // handled accordingly
          if (animationCancelled) {
            applyAnimationClasses(element, options);
            applyAnimationStyles(element, options);
          }

          // if the event changed from something like enter to leave then we do
          // it, otherwise if it's the same then the end result will be the same too
          if (animationCancelled || (isStructural && animationDetails.event !== event)) {
            options.domOperation();
            runner.end();
          }

          // in the event that the element animation was not cancelled or a follow-up animation
          // isn't allowed to animate from here then we need to clear the state of the element
          // so that any future animations won't read the expired animation data.
          if (!isValidAnimation) {
            clearElementAnimationState(node);
          }

          return;
        }

        // this combined multiple class to addClass / removeClass into a setClass event
        // so long as a structural event did not take over the animation
        event = !animationDetails.structural && hasAnimationClasses(animationDetails, true)
            ? 'setClass'
            : animationDetails.event;

        markElementAnimationState(node, RUNNING_STATE);
        var realRunner = $$animation(element, event, animationDetails.options);

        // this will update the runner's flow-control events based on
        // the `realRunner` object.
        runner.setHost(realRunner);
        notifyProgress(runner, event, 'start', {});

        realRunner.done(function(status) {
          close(!status);
          var animationDetails = activeAnimationsLookup.get(node);
          if (animationDetails && animationDetails.counter === counter) {
            clearElementAnimationState(node);
          }
          notifyProgress(runner, event, 'close', {});
        });
      });

      return runner;

      function notifyProgress(runner, event, phase, data) {
        runInNextPostDigestOrNow(function() {
          var callbacks = findCallbacks(parentNode, node, event);
          if (callbacks.length) {
            // do not optimize this call here to RAF because
            // we don't know how heavy the callback code here will
            // be and if this code is buffered then this can
            // lead to a performance regression.
            $$rAF(function() {
              forEach(callbacks, function(callback) {
                callback(element, phase, data);
              });
              cleanupEventListeners(phase, node);
            });
          } else {
            cleanupEventListeners(phase, node);
          }
        });
        runner.progress(event, phase, data);
      }

      function close(reject) {
        clearGeneratedClasses(element, options);
        applyAnimationClasses(element, options);
        applyAnimationStyles(element, options);
        options.domOperation();
        runner.complete(!reject);
      }
    }

    function closeChildAnimations(node) {
      var children = node.querySelectorAll('[' + NG_ANIMATE_ATTR_NAME + ']');
      forEach(children, function(child) {
        var state = parseInt(child.getAttribute(NG_ANIMATE_ATTR_NAME), 10);
        var animationDetails = activeAnimationsLookup.get(child);
        if (animationDetails) {
          switch (state) {
            case RUNNING_STATE:
              animationDetails.runner.end();
              /* falls through */
            case PRE_DIGEST_STATE:
              activeAnimationsLookup.delete(child);
              break;
          }
        }
      });
    }

    function clearElementAnimationState(node) {
      node.removeAttribute(NG_ANIMATE_ATTR_NAME);
      activeAnimationsLookup.delete(node);
    }

    /**
     * This fn returns false if any of the following is true:
     * a) animations on any parent element are disabled, and animations on the element aren't explicitly allowed
     * b) a parent element has an ongoing structural animation, and animateChildren is false
     * c) the element is not a child of the body
     * d) the element is not a child of the $rootElement
     */
    function areAnimationsAllowed(node, parentNode, event) {
      var bodyNode = $document[0].body;
      var rootNode = getDomNode($rootElement);

      var bodyNodeDetected = (node === bodyNode) || node.nodeName === 'HTML';
      var rootNodeDetected = (node === rootNode);
      var parentAnimationDetected = false;
      var elementDisabled = disabledElementsLookup.get(node);
      var animateChildren;

      var parentHost = jqLite.data(node, NG_ANIMATE_PIN_DATA);
      if (parentHost) {
        parentNode = getDomNode(parentHost);
      }

      while (parentNode) {
        if (!rootNodeDetected) {
          // angular doesn't want to attempt to animate elements outside of the application
          // therefore we need to ensure that the rootElement is an ancestor of the current element
          rootNodeDetected = (parentNode === rootNode);
        }

        if (parentNode.nodeType !== ELEMENT_NODE) {
          // no point in inspecting the #document element
          break;
        }

        var details = activeAnimationsLookup.get(parentNode) || {};
        // either an enter, leave or move animation will commence
        // therefore we can't allow any animations to take place
        // but if a parent animation is class-based then that's ok
        if (!parentAnimationDetected) {
          var parentNodeDisabled = disabledElementsLookup.get(parentNode);

          if (parentNodeDisabled === true && elementDisabled !== false) {
            // disable animations if the user hasn't explicitly enabled animations on the
            // current element
            elementDisabled = true;
            // element is disabled via parent element, no need to check anything else
            break;
          } else if (parentNodeDisabled === false) {
            elementDisabled = false;
          }
          parentAnimationDetected = details.structural;
        }

        if (isUndefined(animateChildren) || animateChildren === true) {
          var value = jqLite.data(parentNode, NG_ANIMATE_CHILDREN_DATA);
          if (isDefined(value)) {
            animateChildren = value;
          }
        }

        // there is no need to continue traversing at this point
        if (parentAnimationDetected && animateChildren === false) break;

        if (!bodyNodeDetected) {
          // we also need to ensure that the element is or will be a part of the body element
          // otherwise it is pointless to even issue an animation to be rendered
          bodyNodeDetected = (parentNode === bodyNode);
        }

        if (bodyNodeDetected && rootNodeDetected) {
          // If both body and root have been found, any other checks are pointless,
          // as no animation data should live outside the application
          break;
        }

        if (!rootNodeDetected) {
          // If `rootNode` is not detected, check if `parentNode` is pinned to another element
          parentHost = jqLite.data(parentNode, NG_ANIMATE_PIN_DATA);
          if (parentHost) {
            // The pin target element becomes the next parent element
            parentNode = getDomNode(parentHost);
            continue;
          }
        }

        parentNode = parentNode.parentNode;
      }

      var allowAnimation = (!parentAnimationDetected || animateChildren) && elementDisabled !== true;
      return allowAnimation && rootNodeDetected && bodyNodeDetected;
    }

    function markElementAnimationState(node, state, details) {
      details = details || {};
      details.state = state;

      node.setAttribute(NG_ANIMATE_ATTR_NAME, state);

      var oldValue = activeAnimationsLookup.get(node);
      var newValue = oldValue
          ? extend(oldValue, details)
          : details;
      activeAnimationsLookup.set(node, newValue);
    }
  }];
}];

/* exported $$AnimationProvider */

var $$AnimationProvider = ['$animateProvider', /** @this */ function($animateProvider) {
  var NG_ANIMATE_REF_ATTR = 'ng-animate-ref';

  var drivers = this.drivers = [];

  var RUNNER_STORAGE_KEY = '$$animationRunner';

  function setRunner(element, runner) {
    element.data(RUNNER_STORAGE_KEY, runner);
  }

  function removeRunner(element) {
    element.removeData(RUNNER_STORAGE_KEY);
  }

  function getRunner(element) {
    return element.data(RUNNER_STORAGE_KEY);
  }

  this.$get = ['$$jqLite', '$rootScope', '$injector', '$$AnimateRunner', '$$Map', '$$rAFScheduler',
       function($$jqLite,   $rootScope,   $injector,   $$AnimateRunner,   $$Map,   $$rAFScheduler) {

    var animationQueue = [];
    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);

    function sortAnimations(animations) {
      var tree = { children: [] };
      var i, lookup = new $$Map();

      // this is done first beforehand so that the map
      // is filled with a list of the elements that will be animated
      for (i = 0; i < animations.length; i++) {
        var animation = animations[i];
        lookup.set(animation.domNode, animations[i] = {
          domNode: animation.domNode,
          fn: animation.fn,
          children: []
        });
      }

      for (i = 0; i < animations.length; i++) {
        processNode(animations[i]);
      }

      return flatten(tree);

      function processNode(entry) {
        if (entry.processed) return entry;
        entry.processed = true;

        var elementNode = entry.domNode;
        var parentNode = elementNode.parentNode;
        lookup.set(elementNode, entry);

        var parentEntry;
        while (parentNode) {
          parentEntry = lookup.get(parentNode);
          if (parentEntry) {
            if (!parentEntry.processed) {
              parentEntry = processNode(parentEntry);
            }
            break;
          }
          parentNode = parentNode.parentNode;
        }

        (parentEntry || tree).children.push(entry);
        return entry;
      }

      function flatten(tree) {
        var result = [];
        var queue = [];
        var i;

        for (i = 0; i < tree.children.length; i++) {
          queue.push(tree.children[i]);
        }

        var remainingLevelEntries = queue.length;
        var nextLevelEntries = 0;
        var row = [];

        for (i = 0; i < queue.length; i++) {
          var entry = queue[i];
          if (remainingLevelEntries <= 0) {
            remainingLevelEntries = nextLevelEntries;
            nextLevelEntries = 0;
            result.push(row);
            row = [];
          }
          row.push(entry.fn);
          entry.children.forEach(function(childEntry) {
            nextLevelEntries++;
            queue.push(childEntry);
          });
          remainingLevelEntries--;
        }

        if (row.length) {
          result.push(row);
        }

        return result;
      }
    }

    // TODO(matsko): document the signature in a better way
    return function(element, event, options) {
      options = prepareAnimationOptions(options);
      var isStructural = ['enter', 'move', 'leave'].indexOf(event) >= 0;

      // there is no animation at the current moment, however
      // these runner methods will get later updated with the
      // methods leading into the driver's end/cancel methods
      // for now they just stop the animation from starting
      var runner = new $$AnimateRunner({
        end: function() { close(); },
        cancel: function() { close(true); }
      });

      if (!drivers.length) {
        close();
        return runner;
      }

      setRunner(element, runner);

      var classes = mergeClasses(element.attr('class'), mergeClasses(options.addClass, options.removeClass));
      var tempClasses = options.tempClasses;
      if (tempClasses) {
        classes += ' ' + tempClasses;
        options.tempClasses = null;
      }

      var prepareClassName;
      if (isStructural) {
        prepareClassName = 'ng-' + event + PREPARE_CLASS_SUFFIX;
        $$jqLite.addClass(element, prepareClassName);
      }

      animationQueue.push({
        // this data is used by the postDigest code and passed into
        // the driver step function
        element: element,
        classes: classes,
        event: event,
        structural: isStructural,
        options: options,
        beforeStart: beforeStart,
        close: close
      });

      element.on('$destroy', handleDestroyedElement);

      // we only want there to be one function called within the post digest
      // block. This way we can group animations for all the animations that
      // were apart of the same postDigest flush call.
      if (animationQueue.length > 1) return runner;

      $rootScope.$$postDigest(function() {
        var animations = [];
        forEach(animationQueue, function(entry) {
          // the element was destroyed early on which removed the runner
          // form its storage. This means we can't animate this element
          // at all and it already has been closed due to destruction.
          if (getRunner(entry.element)) {
            animations.push(entry);
          } else {
            entry.close();
          }
        });

        // now any future animations will be in another postDigest
        animationQueue.length = 0;

        var groupedAnimations = groupAnimations(animations);
        var toBeSortedAnimations = [];

        forEach(groupedAnimations, function(animationEntry) {
          toBeSortedAnimations.push({
            domNode: getDomNode(animationEntry.from ? animationEntry.from.element : animationEntry.element),
            fn: function triggerAnimationStart() {
              // it's important that we apply the `ng-animate` CSS class and the
              // temporary classes before we do any driver invoking since these
              // CSS classes may be required for proper CSS detection.
              animationEntry.beforeStart();

              var startAnimationFn, closeFn = animationEntry.close;

              // in the event that the element was removed before the digest runs or
              // during the RAF sequencing then we should not trigger the animation.
              var targetElement = animationEntry.anchors
                  ? (animationEntry.from.element || animationEntry.to.element)
                  : animationEntry.element;

              if (getRunner(targetElement)) {
                var operation = invokeFirstDriver(animationEntry);
                if (operation) {
                  startAnimationFn = operation.start;
                }
              }

              if (!startAnimationFn) {
                closeFn();
              } else {
                var animationRunner = startAnimationFn();
                animationRunner.done(function(status) {
                  closeFn(!status);
                });
                updateAnimationRunners(animationEntry, animationRunner);
              }
            }
          });
        });

        // we need to sort each of the animations in order of parent to child
        // relationships. This ensures that the child classes are applied at the
        // right time.
        $$rAFScheduler(sortAnimations(toBeSortedAnimations));
      });

      return runner;

      // TODO(matsko): change to reference nodes
      function getAnchorNodes(node) {
        var SELECTOR = '[' + NG_ANIMATE_REF_ATTR + ']';
        var items = node.hasAttribute(NG_ANIMATE_REF_ATTR)
              ? [node]
              : node.querySelectorAll(SELECTOR);
        var anchors = [];
        forEach(items, function(node) {
          var attr = node.getAttribute(NG_ANIMATE_REF_ATTR);
          if (attr && attr.length) {
            anchors.push(node);
          }
        });
        return anchors;
      }

      function groupAnimations(animations) {
        var preparedAnimations = [];
        var refLookup = {};
        forEach(animations, function(animation, index) {
          var element = animation.element;
          var node = getDomNode(element);
          var event = animation.event;
          var enterOrMove = ['enter', 'move'].indexOf(event) >= 0;
          var anchorNodes = animation.structural ? getAnchorNodes(node) : [];

          if (anchorNodes.length) {
            var direction = enterOrMove ? 'to' : 'from';

            forEach(anchorNodes, function(anchor) {
              var key = anchor.getAttribute(NG_ANIMATE_REF_ATTR);
              refLookup[key] = refLookup[key] || {};
              refLookup[key][direction] = {
                animationID: index,
                element: jqLite(anchor)
              };
            });
          } else {
            preparedAnimations.push(animation);
          }
        });

        var usedIndicesLookup = {};
        var anchorGroups = {};
        forEach(refLookup, function(operations, key) {
          var from = operations.from;
          var to = operations.to;

          if (!from || !to) {
            // only one of these is set therefore we can't have an
            // anchor animation since all three pieces are required
            var index = from ? from.animationID : to.animationID;
            var indexKey = index.toString();
            if (!usedIndicesLookup[indexKey]) {
              usedIndicesLookup[indexKey] = true;
              preparedAnimations.push(animations[index]);
            }
            return;
          }

          var fromAnimation = animations[from.animationID];
          var toAnimation = animations[to.animationID];
          var lookupKey = from.animationID.toString();
          if (!anchorGroups[lookupKey]) {
            var group = anchorGroups[lookupKey] = {
              structural: true,
              beforeStart: function() {
                fromAnimation.beforeStart();
                toAnimation.beforeStart();
              },
              close: function() {
                fromAnimation.close();
                toAnimation.close();
              },
              classes: cssClassesIntersection(fromAnimation.classes, toAnimation.classes),
              from: fromAnimation,
              to: toAnimation,
              anchors: [] // TODO(matsko): change to reference nodes
            };

            // the anchor animations require that the from and to elements both have at least
            // one shared CSS class which effectively marries the two elements together to use
            // the same animation driver and to properly sequence the anchor animation.
            if (group.classes.length) {
              preparedAnimations.push(group);
            } else {
              preparedAnimations.push(fromAnimation);
              preparedAnimations.push(toAnimation);
            }
          }

          anchorGroups[lookupKey].anchors.push({
            'out': from.element, 'in': to.element
          });
        });

        return preparedAnimations;
      }

      function cssClassesIntersection(a,b) {
        a = a.split(' ');
        b = b.split(' ');
        var matches = [];

        for (var i = 0; i < a.length; i++) {
          var aa = a[i];
          if (aa.substring(0,3) === 'ng-') continue;

          for (var j = 0; j < b.length; j++) {
            if (aa === b[j]) {
              matches.push(aa);
              break;
            }
          }
        }

        return matches.join(' ');
      }

      function invokeFirstDriver(animationDetails) {
        // we loop in reverse order since the more general drivers (like CSS and JS)
        // may attempt more elements, but custom drivers are more particular
        for (var i = drivers.length - 1; i >= 0; i--) {
          var driverName = drivers[i];
          var factory = $injector.get(driverName);
          var driver = factory(animationDetails);
          if (driver) {
            return driver;
          }
        }
      }

      function beforeStart() {
        element.addClass(NG_ANIMATE_CLASSNAME);
        if (tempClasses) {
          $$jqLite.addClass(element, tempClasses);
        }
        if (prepareClassName) {
          $$jqLite.removeClass(element, prepareClassName);
          prepareClassName = null;
        }
      }

      function updateAnimationRunners(animation, newRunner) {
        if (animation.from && animation.to) {
          update(animation.from.element);
          update(animation.to.element);
        } else {
          update(animation.element);
        }

        function update(element) {
          var runner = getRunner(element);
          if (runner) runner.setHost(newRunner);
        }
      }

      function handleDestroyedElement() {
        var runner = getRunner(element);
        if (runner && (event !== 'leave' || !options.$$domOperationFired)) {
          runner.end();
        }
      }

      function close(rejected) {
        element.off('$destroy', handleDestroyedElement);
        removeRunner(element);

        applyAnimationClasses(element, options);
        applyAnimationStyles(element, options);
        options.domOperation();

        if (tempClasses) {
          $$jqLite.removeClass(element, tempClasses);
        }

        element.removeClass(NG_ANIMATE_CLASSNAME);
        runner.complete(!rejected);
      }
    };
  }];
}];

/**
 * @ngdoc directive
 * @name ngAnimateSwap
 * @restrict A
 * @scope
 *
 * @description
 *
 * ngAnimateSwap is a animation-oriented directive that allows for the container to
 * be removed and entered in whenever the associated expression changes. A
 * common usecase for this directive is a rotating banner or slider component which
 * contains one image being present at a time. When the active image changes
 * then the old image will perform a `leave` animation and the new element
 * will be inserted via an `enter` animation.
 *
 * @animations
 * | Animation                        | Occurs                               |
 * |----------------------------------|--------------------------------------|
 * | {@link ng.$animate#enter enter}  | when the new element is inserted to the DOM  |
 * | {@link ng.$animate#leave leave}  | when the old element is removed from the DOM |
 *
 * @example
 * <example name="ngAnimateSwap-directive" module="ngAnimateSwapExample"
 *          deps="angular-animate.js"
 *          animations="true" fixBase="true">
 *   <file name="index.html">
 *     <div class="container" ng-controller="AppCtrl">
 *       <div ng-animate-swap="number" class="cell swap-animation" ng-class="colorClass(number)">
 *         {{ number }}
 *       </div>
 *     </div>
 *   </file>
 *   <file name="script.js">
 *     angular.module('ngAnimateSwapExample', ['ngAnimate'])
 *       .controller('AppCtrl', ['$scope', '$interval', function($scope, $interval) {
 *         $scope.number = 0;
 *         $interval(function() {
 *           $scope.number++;
 *         }, 1000);
 *
 *         var colors = ['red','blue','green','yellow','orange'];
 *         $scope.colorClass = function(number) {
 *           return colors[number % colors.length];
 *         };
 *       }]);
 *   </file>
 *  <file name="animations.css">
 *  .container {
 *    height:250px;
 *    width:250px;
 *    position:relative;
 *    overflow:hidden;
 *    border:2px solid black;
 *  }
 *  .container .cell {
 *    font-size:150px;
 *    text-align:center;
 *    line-height:250px;
 *    position:absolute;
 *    top:0;
 *    left:0;
 *    right:0;
 *    border-bottom:2px solid black;
 *  }
 *  .swap-animation.ng-enter, .swap-animation.ng-leave {
 *    transition:0.5s linear all;
 *  }
 *  .swap-animation.ng-enter {
 *    top:-250px;
 *  }
 *  .swap-animation.ng-enter-active {
 *    top:0px;
 *  }
 *  .swap-animation.ng-leave {
 *    top:0px;
 *  }
 *  .swap-animation.ng-leave-active {
 *    top:250px;
 *  }
 *  .red { background:red; }
 *  .green { background:green; }
 *  .blue { background:blue; }
 *  .yellow { background:yellow; }
 *  .orange { background:orange; }
 *  </file>
 * </example>
 */
var ngAnimateSwapDirective = ['$animate', '$rootScope', function($animate, $rootScope) {
  return {
    restrict: 'A',
    transclude: 'element',
    terminal: true,
    priority: 600, // we use 600 here to ensure that the directive is caught before others
    link: function(scope, $element, attrs, ctrl, $transclude) {
      var previousElement, previousScope;
      scope.$watchCollection(attrs.ngAnimateSwap || attrs['for'], function(value) {
        if (previousElement) {
          $animate.leave(previousElement);
        }
        if (previousScope) {
          previousScope.$destroy();
          previousScope = null;
        }
        if (value || value === 0) {
          previousScope = scope.$new();
          $transclude(previousScope, function(element) {
            previousElement = element;
            $animate.enter(element, null, $element);
          });
        }
      });
    }
  };
}];

/**
 * @ngdoc module
 * @name ngAnimate
 * @description
 *
 * The `ngAnimate` module provides support for CSS-based animations (keyframes and transitions) as well as JavaScript-based animations via
 * callback hooks. Animations are not enabled by default, however, by including `ngAnimate` the animation hooks are enabled for an Angular app.
 *
 * <div doc-module-components="ngAnimate"></div>
 *
 * # Usage
 * Simply put, there are two ways to make use of animations when ngAnimate is used: by using **CSS** and **JavaScript**. The former works purely based
 * using CSS (by using matching CSS selectors/styles) and the latter triggers animations that are registered via `module.animation()`. For
 * both CSS and JS animations the sole requirement is to have a matching `CSS class` that exists both in the registered animation and within
 * the HTML element that the animation will be triggered on.
 *
 * ## Directive Support
 * The following directives are "animation aware":
 *
 * | Directive                                                                                                | Supported Animations                                                     |
 * |----------------------------------------------------------------------------------------------------------|--------------------------------------------------------------------------|
 * | {@link ng.directive:ngRepeat#animations ngRepeat}                                                        | enter, leave and move                                                    |
 * | {@link ngRoute.directive:ngView#animations ngView}                                                       | enter and leave                                                          |
 * | {@link ng.directive:ngInclude#animations ngInclude}                                                      | enter and leave                                                          |
 * | {@link ng.directive:ngSwitch#animations ngSwitch}                                                        | enter and leave                                                          |
 * | {@link ng.directive:ngIf#animations ngIf}                                                                | enter and leave                                                          |
 * | {@link ng.directive:ngClass#animations ngClass}                                                          | add and remove (the CSS class(es) present)                               |
 * | {@link ng.directive:ngShow#animations ngShow} & {@link ng.directive:ngHide#animations ngHide}            | add and remove (the ng-hide class value)                                 |
 * | {@link ng.directive:form#animation-hooks form} & {@link ng.directive:ngModel#animation-hooks ngModel}    | add and remove (dirty, pristine, valid, invalid & all other validations) |
 * | {@link module:ngMessages#animations ngMessages}                                                          | add and remove (ng-active & ng-inactive)                                 |
 * | {@link module:ngMessages#animations ngMessage}                                                           | enter and leave                                                          |
 *
 * (More information can be found by visiting each the documentation associated with each directive.)
 *
 * ## CSS-based Animations
 *
 * CSS-based animations with ngAnimate are unique since they require no JavaScript code at all. By using a CSS class that we reference between our HTML
 * and CSS code we can create an animation that will be picked up by Angular when an underlying directive performs an operation.
 *
 * The example below shows how an `enter` animation can be made possible on an element using `ng-if`:
 *
 * ```html
 * <div ng-if="bool" class="fade">
 *    Fade me in out
 * </div>
 * <button ng-click="bool=true">Fade In!</button>
 * <button ng-click="bool=false">Fade Out!</button>
 * ```
 *
 * Notice the CSS class **fade**? We can now create the CSS transition code that references this class:
 *
 * ```css
 * /&#42; The starting CSS styles for the enter animation &#42;/
 * .fade.ng-enter {
 *   transition:0.5s linear all;
 *   opacity:0;
 * }
 *
 * /&#42; The finishing CSS styles for the enter animation &#42;/
 * .fade.ng-enter.ng-enter-active {
 *   opacity:1;
 * }
 * ```
 *
 * The key thing to remember here is that, depending on the animation event (which each of the directives above trigger depending on what's going on) two
 * generated CSS classes will be applied to the element; in the example above we have `.ng-enter` and `.ng-enter-active`. For CSS transitions, the transition
 * code **must** be defined within the starting CSS class (in this case `.ng-enter`). The destination class is what the transition will animate towards.
 *
 * If for example we wanted to create animations for `leave` and `move` (ngRepeat triggers move) then we can do so using the same CSS naming conventions:
 *
 * ```css
 * /&#42; now the element will fade out before it is removed from the DOM &#42;/
 * .fade.ng-leave {
 *   transition:0.5s linear all;
 *   opacity:1;
 * }
 * .fade.ng-leave.ng-leave-active {
 *   opacity:0;
 * }
 * ```
 *
 * We can also make use of **CSS Keyframes** by referencing the keyframe animation within the starting CSS class:
 *
 * ```css
 * /&#42; there is no need to define anything inside of the destination
 * CSS class since the keyframe will take charge of the animation &#42;/
 * .fade.ng-leave {
 *   animation: my_fade_animation 0.5s linear;
 *   -webkit-animation: my_fade_animation 0.5s linear;
 * }
 *
 * @keyframes my_fade_animation {
 *   from { opacity:1; }
 *   to { opacity:0; }
 * }
 *
 * @-webkit-keyframes my_fade_animation {
 *   from { opacity:1; }
 *   to { opacity:0; }
 * }
 * ```
 *
 * Feel free also mix transitions and keyframes together as well as any other CSS classes on the same element.
 *
 * ### CSS Class-based Animations
 *
 * Class-based animations (animations that are triggered via `ngClass`, `ngShow`, `ngHide` and some other directives) have a slightly different
 * naming convention. Class-based animations are basic enough that a standard transition or keyframe can be referenced on the class being added
 * and removed.
 *
 * For example if we wanted to do a CSS animation for `ngHide` then we place an animation on the `.ng-hide` CSS class:
 *
 * ```html
 * <div ng-show="bool" class="fade">
 *   Show and hide me
 * </div>
 * <button ng-click="bool=!bool">Toggle</button>
 *
 * <style>
 * .fade.ng-hide {
 *   transition:0.5s linear all;
 *   opacity:0;
 * }
 * </style>
 * ```
 *
 * All that is going on here with ngShow/ngHide behind the scenes is the `.ng-hide` class is added/removed (when the hidden state is valid). Since
 * ngShow and ngHide are animation aware then we can match up a transition and ngAnimate handles the rest.
 *
 * In addition the addition and removal of the CSS class, ngAnimate also provides two helper methods that we can use to further decorate the animation
 * with CSS styles.
 *
 * ```html
 * <div ng-class="{on:onOff}" class="highlight">
 *   Highlight this box
 * </div>
 * <button ng-click="onOff=!onOff">Toggle</button>
 *
 * <style>
 * .highlight {
 *   transition:0.5s linear all;
 * }
 * .highlight.on-add {
 *   background:white;
 * }
 * .highlight.on {
 *   background:yellow;
 * }
 * .highlight.on-remove {
 *   background:black;
 * }
 * </style>
 * ```
 *
 * We can also make use of CSS keyframes by placing them within the CSS classes.
 *
 *
 * ### CSS Staggering Animations
 * A Staggering animation is a collection of animations that are issued with a slight delay in between each successive operation resulting in a
 * curtain-like effect. The ngAnimate module (versions >=1.2) supports staggering animations and the stagger effect can be
 * performed by creating a **ng-EVENT-stagger** CSS class and attaching that class to the base CSS class used for
 * the animation. The style property expected within the stagger class can either be a **transition-delay** or an
 * **animation-delay** property (or both if your animation contains both transitions and keyframe animations).
 *
 * ```css
 * .my-animation.ng-enter {
 *   /&#42; standard transition code &#42;/
 *   transition: 1s linear all;
 *   opacity:0;
 * }
 * .my-animation.ng-enter-stagger {
 *   /&#42; this will have a 100ms delay between each successive leave animation &#42;/
 *   transition-delay: 0.1s;
 *
 *   /&#42; As of 1.4.4, this must always be set: it signals ngAnimate
 *     to not accidentally inherit a delay property from another CSS class &#42;/
 *   transition-duration: 0s;
 *
 *   /&#42; if you are using animations instead of transitions you should configure as follows:
 *     animation-delay: 0.1s;
 *     animation-duration: 0s; &#42;/
 * }
 * .my-animation.ng-enter.ng-enter-active {
 *   /&#42; standard transition styles &#42;/
 *   opacity:1;
 * }
 * ```
 *
 * Staggering animations work by default in ngRepeat (so long as the CSS class is defined). Outside of ngRepeat, to use staggering animations
 * on your own, they can be triggered by firing multiple calls to the same event on $animate. However, the restrictions surrounding this
 * are that each of the elements must have the same CSS className value as well as the same parent element. A stagger operation
 * will also be reset if one or more animation frames have passed since the multiple calls to `$animate` were fired.
 *
 * The following code will issue the **ng-leave-stagger** event on the element provided:
 *
 * ```js
 * var kids = parent.children();
 *
 * $animate.leave(kids[0]); //stagger index=0
 * $animate.leave(kids[1]); //stagger index=1
 * $animate.leave(kids[2]); //stagger index=2
 * $animate.leave(kids[3]); //stagger index=3
 * $animate.leave(kids[4]); //stagger index=4
 *
 * window.requestAnimationFrame(function() {
 *   //stagger has reset itself
 *   $animate.leave(kids[5]); //stagger index=0
 *   $animate.leave(kids[6]); //stagger index=1
 *
 *   $scope.$digest();
 * });
 * ```
 *
 * Stagger animations are currently only supported within CSS-defined animations.
 *
 * ### The `ng-animate` CSS class
 *
 * When ngAnimate is animating an element it will apply the `ng-animate` CSS class to the element for the duration of the animation.
 * This is a temporary CSS class and it will be removed once the animation is over (for both JavaScript and CSS-based animations).
 *
 * Therefore, animations can be applied to an element using this temporary class directly via CSS.
 *
 * ```css
 * .zipper.ng-animate {
 *   transition:0.5s linear all;
 * }
 * .zipper.ng-enter {
 *   opacity:0;
 * }
 * .zipper.ng-enter.ng-enter-active {
 *   opacity:1;
 * }
 * .zipper.ng-leave {
 *   opacity:1;
 * }
 * .zipper.ng-leave.ng-leave-active {
 *   opacity:0;
 * }
 * ```
 *
 * (Note that the `ng-animate` CSS class is reserved and it cannot be applied on an element directly since ngAnimate will always remove
 * the CSS class once an animation has completed.)
 *
 *
 * ### The `ng-[event]-prepare` class
 *
 * This is a special class that can be used to prevent unwanted flickering / flash of content before
 * the actual animation starts. The class is added as soon as an animation is initialized, but removed
 * before the actual animation starts (after waiting for a $digest).
 * It is also only added for *structural* animations (`enter`, `move`, and `leave`).
 *
 * In practice, flickering can appear when nesting elements with structural animations such as `ngIf`
 * into elements that have class-based animations such as `ngClass`.
 *
 * ```html
 * <div ng-class="{red: myProp}">
 *   <div ng-class="{blue: myProp}">
 *     <div class="message" ng-if="myProp"></div>
 *   </div>
 * </div>
 * ```
 *
 * It is possible that during the `enter` animation, the `.message` div will be briefly visible before it starts animating.
 * In that case, you can add styles to the CSS that make sure the element stays hidden before the animation starts:
 *
 * ```css
 * .message.ng-enter-prepare {
 *   opacity: 0;
 * }
 *
 * ```
 *
 * ## JavaScript-based Animations
 *
 * ngAnimate also allows for animations to be consumed by JavaScript code. The approach is similar to CSS-based animations (where there is a shared
 * CSS class that is referenced in our HTML code) but in addition we need to register the JavaScript animation on the module. By making use of the
 * `module.animation()` module function we can register the animation.
 *
 * Let's see an example of a enter/leave animation using `ngRepeat`:
 *
 * ```html
 * <div ng-repeat="item in items" class="slide">
 *   {{ item }}
 * </div>
 * ```
 *
 * See the **slide** CSS class? Let's use that class to define an animation that we'll structure in our module code by using `module.animation`:
 *
 * ```js
 * myModule.animation('.slide', [function() {
 *   return {
 *     // make note that other events (like addClass/removeClass)
 *     // have different function input parameters
 *     enter: function(element, doneFn) {
 *       jQuery(element).fadeIn(1000, doneFn);
 *
 *       // remember to call doneFn so that angular
 *       // knows that the animation has concluded
 *     },
 *
 *     move: function(element, doneFn) {
 *       jQuery(element).fadeIn(1000, doneFn);
 *     },
 *
 *     leave: function(element, doneFn) {
 *       jQuery(element).fadeOut(1000, doneFn);
 *     }
 *   }
 * }]);
 * ```
 *
 * The nice thing about JS-based animations is that we can inject other services and make use of advanced animation libraries such as
 * greensock.js and velocity.js.
 *
 * If our animation code class-based (meaning that something like `ngClass`, `ngHide` and `ngShow` triggers it) then we can still define
 * our animations inside of the same registered animation, however, the function input arguments are a bit different:
 *
 * ```html
 * <div ng-class="color" class="colorful">
 *   this box is moody
 * </div>
 * <button ng-click="color='red'">Change to red</button>
 * <button ng-click="color='blue'">Change to blue</button>
 * <button ng-click="color='green'">Change to green</button>
 * ```
 *
 * ```js
 * myModule.animation('.colorful', [function() {
 *   return {
 *     addClass: function(element, className, doneFn) {
 *       // do some cool animation and call the doneFn
 *     },
 *     removeClass: function(element, className, doneFn) {
 *       // do some cool animation and call the doneFn
 *     },
 *     setClass: function(element, addedClass, removedClass, doneFn) {
 *       // do some cool animation and call the doneFn
 *     }
 *   }
 * }]);
 * ```
 *
 * ## CSS + JS Animations Together
 *
 * AngularJS 1.4 and higher has taken steps to make the amalgamation of CSS and JS animations more flexible. However, unlike earlier versions of Angular,
 * defining CSS and JS animations to work off of the same CSS class will not work anymore. Therefore the example below will only result in **JS animations taking
 * charge of the animation**:
 *
 * ```html
 * <div ng-if="bool" class="slide">
 *   Slide in and out
 * </div>
 * ```
 *
 * ```js
 * myModule.animation('.slide', [function() {
 *   return {
 *     enter: function(element, doneFn) {
 *       jQuery(element).slideIn(1000, doneFn);
 *     }
 *   }
 * }]);
 * ```
 *
 * ```css
 * .slide.ng-enter {
 *   transition:0.5s linear all;
 *   transform:translateY(-100px);
 * }
 * .slide.ng-enter.ng-enter-active {
 *   transform:translateY(0);
 * }
 * ```
 *
 * Does this mean that CSS and JS animations cannot be used together? Do JS-based animations always have higher priority? We can make up for the
 * lack of CSS animations by using the `$animateCss` service to trigger our own tweaked-out, CSS-based animations directly from
 * our own JS-based animation code:
 *
 * ```js
 * myModule.animation('.slide', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element) {
*        // this will trigger `.slide.ng-enter` and `.slide.ng-enter-active`.
 *       return $animateCss(element, {
 *         event: 'enter',
 *         structural: true
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * The nice thing here is that we can save bandwidth by sticking to our CSS-based animation code and we don't need to rely on a 3rd-party animation framework.
 *
 * The `$animateCss` service is very powerful since we can feed in all kinds of extra properties that will be evaluated and fed into a CSS transition or
 * keyframe animation. For example if we wanted to animate the height of an element while adding and removing classes then we can do so by providing that
 * data into `$animateCss` directly:
 *
 * ```js
 * myModule.animation('.slide', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element) {
 *       return $animateCss(element, {
 *         event: 'enter',
 *         structural: true,
 *         addClass: 'maroon-setting',
 *         from: { height:0 },
 *         to: { height: 200 }
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * Now we can fill in the rest via our transition CSS code:
 *
 * ```css
 * /&#42; the transition tells ngAnimate to make the animation happen &#42;/
 * .slide.ng-enter { transition:0.5s linear all; }
 *
 * /&#42; this extra CSS class will be absorbed into the transition
 * since the $animateCss code is adding the class &#42;/
 * .maroon-setting { background:red; }
 * ```
 *
 * And `$animateCss` will figure out the rest. Just make sure to have the `done()` callback fire the `doneFn` function to signal when the animation is over.
 *
 * To learn more about what's possible be sure to visit the {@link ngAnimate.$animateCss $animateCss service}.
 *
 * ## Animation Anchoring (via `ng-animate-ref`)
 *
 * ngAnimate in AngularJS 1.4 comes packed with the ability to cross-animate elements between
 * structural areas of an application (like views) by pairing up elements using an attribute
 * called `ng-animate-ref`.
 *
 * Let's say for example we have two views that are managed by `ng-view` and we want to show
 * that there is a relationship between two components situated in within these views. By using the
 * `ng-animate-ref` attribute we can identify that the two components are paired together and we
 * can then attach an animation, which is triggered when the view changes.
 *
 * Say for example we have the following template code:
 *
 * ```html
 * <!-- index.html -->
 * <div ng-view class="view-animation">
 * </div>
 *
 * <!-- home.html -->
 * <a href="#/banner-page">
 *   <img src="./banner.jpg" class="banner" ng-animate-ref="banner">
 * </a>
 *
 * <!-- banner-page.html -->
 * <img src="./banner.jpg" class="banner" ng-animate-ref="banner">
 * ```
 *
 * Now, when the view changes (once the link is clicked), ngAnimate will examine the
 * HTML contents to see if there is a match reference between any components in the view
 * that is leaving and the view that is entering. It will scan both the view which is being
 * removed (leave) and inserted (enter) to see if there are any paired DOM elements that
 * contain a matching ref value.
 *
 * The two images match since they share the same ref value. ngAnimate will now create a
 * transport element (which is a clone of the first image element) and it will then attempt
 * to animate to the position of the second image element in the next view. For the animation to
 * work a special CSS class called `ng-anchor` will be added to the transported element.
 *
 * We can now attach a transition onto the `.banner.ng-anchor` CSS class and then
 * ngAnimate will handle the entire transition for us as well as the addition and removal of
 * any changes of CSS classes between the elements:
 *
 * ```css
 * .banner.ng-anchor {
 *   /&#42; this animation will last for 1 second since there are
 *          two phases to the animation (an `in` and an `out` phase) &#42;/
 *   transition:0.5s linear all;
 * }
 * ```
 *
 * We also **must** include animations for the views that are being entered and removed
 * (otherwise anchoring wouldn't be possible since the new view would be inserted right away).
 *
 * ```css
 * .view-animation.ng-enter, .view-animation.ng-leave {
 *   transition:0.5s linear all;
 *   position:fixed;
 *   left:0;
 *   top:0;
 *   width:100%;
 * }
 * .view-animation.ng-enter {
 *   transform:translateX(100%);
 * }
 * .view-animation.ng-leave,
 * .view-animation.ng-enter.ng-enter-active {
 *   transform:translateX(0%);
 * }
 * .view-animation.ng-leave.ng-leave-active {
 *   transform:translateX(-100%);
 * }
 * ```
 *
 * Now we can jump back to the anchor animation. When the animation happens, there are two stages that occur:
 * an `out` and an `in` stage. The `out` stage happens first and that is when the element is animated away
 * from its origin. Once that animation is over then the `in` stage occurs which animates the
 * element to its destination. The reason why there are two animations is to give enough time
 * for the enter animation on the new element to be ready.
 *
 * The example above sets up a transition for both the in and out phases, but we can also target the out or
 * in phases directly via `ng-anchor-out` and `ng-anchor-in`.
 *
 * ```css
 * .banner.ng-anchor-out {
 *   transition: 0.5s linear all;
 *
 *   /&#42; the scale will be applied during the out animation,
 *          but will be animated away when the in animation runs &#42;/
 *   transform: scale(1.2);
 * }
 *
 * .banner.ng-anchor-in {
 *   transition: 1s linear all;
 * }
 * ```
 *
 *
 *
 *
 * ### Anchoring Demo
 *
  <example module="anchoringExample"
           name="anchoringExample"
           id="anchoringExample"
           deps="angular-animate.js;angular-route.js"
           animations="true">
    <file name="index.html">
      <a href="#!/">Home</a>
      <hr />
      <div class="view-container">
        <div ng-view class="view"></div>
      </div>
    </file>
    <file name="script.js">
      angular.module('anchoringExample', ['ngAnimate', 'ngRoute'])
        .config(['$routeProvider', function($routeProvider) {
          $routeProvider.when('/', {
            templateUrl: 'home.html',
            controller: 'HomeController as home'
          });
          $routeProvider.when('/profile/:id', {
            templateUrl: 'profile.html',
            controller: 'ProfileController as profile'
          });
        }])
        .run(['$rootScope', function($rootScope) {
          $rootScope.records = [
            { id: 1, title: 'Miss Beulah Roob' },
            { id: 2, title: 'Trent Morissette' },
            { id: 3, title: 'Miss Ava Pouros' },
            { id: 4, title: 'Rod Pouros' },
            { id: 5, title: 'Abdul Rice' },
            { id: 6, title: 'Laurie Rutherford Sr.' },
            { id: 7, title: 'Nakia McLaughlin' },
            { id: 8, title: 'Jordon Blanda DVM' },
            { id: 9, title: 'Rhoda Hand' },
            { id: 10, title: 'Alexandrea Sauer' }
          ];
        }])
        .controller('HomeController', [function() {
          //empty
        }])
        .controller('ProfileController', ['$rootScope', '$routeParams',
            function ProfileController($rootScope, $routeParams) {
          var index = parseInt($routeParams.id, 10);
          var record = $rootScope.records[index - 1];

          this.title = record.title;
          this.id = record.id;
        }]);
    </file>
    <file name="home.html">
      <h2>Welcome to the home page</h1>
      <p>Please click on an element</p>
      <a class="record"
         ng-href="#!/profile/{{ record.id }}"
         ng-animate-ref="{{ record.id }}"
         ng-repeat="record in records">
        {{ record.title }}
      </a>
    </file>
    <file name="profile.html">
      <div class="profile record" ng-animate-ref="{{ profile.id }}">
        {{ profile.title }}
      </div>
    </file>
    <file name="animations.css">
      .record {
        display:block;
        font-size:20px;
      }
      .profile {
        background:black;
        color:white;
        font-size:100px;
      }
      .view-container {
        position:relative;
      }
      .view-container > .view.ng-animate {
        position:absolute;
        top:0;
        left:0;
        width:100%;
        min-height:500px;
      }
      .view.ng-enter, .view.ng-leave,
      .record.ng-anchor {
        transition:0.5s linear all;
      }
      .view.ng-enter {
        transform:translateX(100%);
      }
      .view.ng-enter.ng-enter-active, .view.ng-leave {
        transform:translateX(0%);
      }
      .view.ng-leave.ng-leave-active {
        transform:translateX(-100%);
      }
      .record.ng-anchor-out {
        background:red;
      }
    </file>
  </example>
 *
 * ### How is the element transported?
 *
 * When an anchor animation occurs, ngAnimate will clone the starting element and position it exactly where the starting
 * element is located on screen via absolute positioning. The cloned element will be placed inside of the root element
 * of the application (where ng-app was defined) and all of the CSS classes of the starting element will be applied. The
 * element will then animate into the `out` and `in` animations and will eventually reach the coordinates and match
 * the dimensions of the destination element. During the entire animation a CSS class of `.ng-animate-shim` will be applied
 * to both the starting and destination elements in order to hide them from being visible (the CSS styling for the class
 * is: `visibility:hidden`). Once the anchor reaches its destination then it will be removed and the destination element
 * will become visible since the shim class will be removed.
 *
 * ### How is the morphing handled?
 *
 * CSS Anchoring relies on transitions and keyframes and the internal code is intelligent enough to figure out
 * what CSS classes differ between the starting element and the destination element. These different CSS classes
 * will be added/removed on the anchor element and a transition will be applied (the transition that is provided
 * in the anchor class). Long story short, ngAnimate will figure out what classes to add and remove which will
 * make the transition of the element as smooth and automatic as possible. Be sure to use simple CSS classes that
 * do not rely on DOM nesting structure so that the anchor element appears the same as the starting element (since
 * the cloned element is placed inside of root element which is likely close to the body element).
 *
 * Note that if the root element is on the `<html>` element then the cloned node will be placed inside of body.
 *
 *
 * ## Using $animate in your directive code
 *
 * So far we've explored how to feed in animations into an Angular application, but how do we trigger animations within our own directives in our application?
 * By injecting the `$animate` service into our directive code, we can trigger structural and class-based hooks which can then be consumed by animations. Let's
 * imagine we have a greeting box that shows and hides itself when the data changes
 *
 * ```html
 * <greeting-box active="onOrOff">Hi there</greeting-box>
 * ```
 *
 * ```js
 * ngModule.directive('greetingBox', ['$animate', function($animate) {
 *   return function(scope, element, attrs) {
 *     attrs.$observe('active', function(value) {
 *       value ? $animate.addClass(element, 'on') : $animate.removeClass(element, 'on');
 *     });
 *   });
 * }]);
 * ```
 *
 * Now the `on` CSS class is added and removed on the greeting box component. Now if we add a CSS class on top of the greeting box element
 * in our HTML code then we can trigger a CSS or JS animation to happen.
 *
 * ```css
 * /&#42; normally we would create a CSS class to reference on the element &#42;/
 * greeting-box.on { transition:0.5s linear all; background:green; color:white; }
 * ```
 *
 * The `$animate` service contains a variety of other methods like `enter`, `leave`, `animate` and `setClass`. To learn more about what's
 * possible be sure to visit the {@link ng.$animate $animate service API page}.
 *
 *
 * ## Callbacks and Promises
 *
 * When `$animate` is called it returns a promise that can be used to capture when the animation has ended. Therefore if we were to trigger
 * an animation (within our directive code) then we can continue performing directive and scope related activities after the animation has
 * ended by chaining onto the returned promise that animation method returns.
 *
 * ```js
 * // somewhere within the depths of the directive
 * $animate.enter(element, parent).then(function() {
 *   //the animation has completed
 * });
 * ```
 *
 * (Note that earlier versions of Angular prior to v1.4 required the promise code to be wrapped using `$scope.$apply(...)`. This is not the case
 * anymore.)
 *
 * In addition to the animation promise, we can also make use of animation-related callbacks within our directives and controller code by registering
 * an event listener using the `$animate` service. Let's say for example that an animation was triggered on our view
 * routing controller to hook into that:
 *
 * ```js
 * ngModule.controller('HomePageController', ['$animate', function($animate) {
 *   $animate.on('enter', ngViewElement, function(element) {
 *     // the animation for this route has completed
 *   }]);
 * }])
 * ```
 *
 * (Note that you will need to trigger a digest within the callback to get angular to notice any scope-related changes.)
 */

var copy;
var extend;
var forEach;
var isArray;
var isDefined;
var isElement;
var isFunction;
var isObject;
var isString;
var isUndefined;
var jqLite;
var noop;

/**
 * @ngdoc service
 * @name $animate
 * @kind object
 *
 * @description
 * The ngAnimate `$animate` service documentation is the same for the core `$animate` service.
 *
 * Click here {@link ng.$animate to learn more about animations with `$animate`}.
 */
angular.module('ngAnimate', [], function initAngularHelpers() {
  // Access helpers from angular core.
  // Do it inside a `config` block to ensure `window.angular` is available.
  noop        = angular.noop;
  copy        = angular.copy;
  extend      = angular.extend;
  jqLite      = angular.element;
  forEach     = angular.forEach;
  isArray     = angular.isArray;
  isString    = angular.isString;
  isObject    = angular.isObject;
  isUndefined = angular.isUndefined;
  isDefined   = angular.isDefined;
  isFunction  = angular.isFunction;
  isElement   = angular.isElement;
})
  .info({ angularVersion: '1.6.3' })
  .directive('ngAnimateSwap', ngAnimateSwapDirective)

  .directive('ngAnimateChildren', $$AnimateChildrenDirective)
  .factory('$$rAFScheduler', $$rAFSchedulerFactory)

  .provider('$$animateQueue', $$AnimateQueueProvider)
  .provider('$$animation', $$AnimationProvider)

  .provider('$animateCss', $AnimateCssProvider)
  .provider('$$animateCssDriver', $$AnimateCssDriverProvider)

  .provider('$$animateJs', $$AnimateJsProvider)
  .provider('$$animateJsDriver', $$AnimateJsDriverProvider);


})(window, window.angular);

/**
 * @license AngularJS v1.6.3
 * (c) 2010-2017 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/**
 * @ngdoc module
 * @name ngAria
 * @description
 *
 * The `ngAria` module provides support for common
 * [<abbr title="Accessible Rich Internet Applications">ARIA</abbr>](http://www.w3.org/TR/wai-aria/)
 * attributes that convey state or semantic information about the application for users
 * of assistive technologies, such as screen readers.
 *
 * <div doc-module-components="ngAria"></div>
 *
 * ## Usage
 *
 * For ngAria to do its magic, simply include the module `ngAria` as a dependency. The following
 * directives are supported:
 * `ngModel`, `ngChecked`, `ngReadonly`, `ngRequired`, `ngValue`, `ngDisabled`, `ngShow`, `ngHide`, `ngClick`,
 * `ngDblClick`, and `ngMessages`.
 *
 * Below is a more detailed breakdown of the attributes handled by ngAria:
 *
 * | Directive                                   | Supported Attributes                                                                                |
 * |---------------------------------------------|-----------------------------------------------------------------------------------------------------|
 * | {@link ng.directive:ngModel ngModel}        | aria-checked, aria-valuemin, aria-valuemax, aria-valuenow, aria-invalid, aria-required, input roles |
 * | {@link ng.directive:ngDisabled ngDisabled}  | aria-disabled                                                                                       |
 * | {@link ng.directive:ngRequired ngRequired}  | aria-required                                                                                       |
 * | {@link ng.directive:ngChecked ngChecked}    | aria-checked                                                                                        |
 * | {@link ng.directive:ngReadonly ngReadonly}  | aria-readonly                                                                                       |
 * | {@link ng.directive:ngValue ngValue}        | aria-checked                                                                                        |
 * | {@link ng.directive:ngShow ngShow}          | aria-hidden                                                                                         |
 * | {@link ng.directive:ngHide ngHide}          | aria-hidden                                                                                         |
 * | {@link ng.directive:ngDblclick ngDblclick}  | tabindex                                                                                            |
 * | {@link module:ngMessages ngMessages}        | aria-live                                                                                           |
 * | {@link ng.directive:ngClick ngClick}        | tabindex, keydown event, button role                                                                |
 *
 * Find out more information about each directive by reading the
 * {@link guide/accessibility ngAria Developer Guide}.
 *
 * ## Example
 * Using ngDisabled with ngAria:
 * ```html
 * <md-checkbox ng-disabled="disabled">
 * ```
 * Becomes:
 * ```html
 * <md-checkbox ng-disabled="disabled" aria-disabled="true">
 * ```
 *
 * ## Disabling Attributes
 * It's possible to disable individual attributes added by ngAria with the
 * {@link ngAria.$ariaProvider#config config} method. For more details, see the
 * {@link guide/accessibility Developer Guide}.
 */
var ngAriaModule = angular.module('ngAria', ['ng']).
                        info({ angularVersion: '1.6.3' }).
                        provider('$aria', $AriaProvider);

/**
* Internal Utilities
*/
var nodeBlackList = ['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT', 'DETAILS', 'SUMMARY'];

var isNodeOneOf = function(elem, nodeTypeArray) {
  if (nodeTypeArray.indexOf(elem[0].nodeName) !== -1) {
    return true;
  }
};
/**
 * @ngdoc provider
 * @name $ariaProvider
 * @this
 *
 * @description
 *
 * Used for configuring the ARIA attributes injected and managed by ngAria.
 *
 * ```js
 * angular.module('myApp', ['ngAria'], function config($ariaProvider) {
 *   $ariaProvider.config({
 *     ariaValue: true,
 *     tabindex: false
 *   });
 * });
 *```
 *
 * ## Dependencies
 * Requires the {@link ngAria} module to be installed.
 *
 */
function $AriaProvider() {
  var config = {
    ariaHidden: true,
    ariaChecked: true,
    ariaReadonly: true,
    ariaDisabled: true,
    ariaRequired: true,
    ariaInvalid: true,
    ariaValue: true,
    tabindex: true,
    bindKeydown: true,
    bindRoleForClick: true
  };

  /**
   * @ngdoc method
   * @name $ariaProvider#config
   *
   * @param {object} config object to enable/disable specific ARIA attributes
   *
   *  - **ariaHidden** – `{boolean}` – Enables/disables aria-hidden tags
   *  - **ariaChecked** – `{boolean}` – Enables/disables aria-checked tags
   *  - **ariaReadonly** – `{boolean}` – Enables/disables aria-readonly tags
   *  - **ariaDisabled** – `{boolean}` – Enables/disables aria-disabled tags
   *  - **ariaRequired** – `{boolean}` – Enables/disables aria-required tags
   *  - **ariaInvalid** – `{boolean}` – Enables/disables aria-invalid tags
   *  - **ariaValue** – `{boolean}` – Enables/disables aria-valuemin, aria-valuemax and
   *    aria-valuenow tags
   *  - **tabindex** – `{boolean}` – Enables/disables tabindex tags
   *  - **bindKeydown** – `{boolean}` – Enables/disables keyboard event binding on non-interactive
   *    elements (such as `div` or `li`) using ng-click, making them more accessible to users of
   *    assistive technologies
   *  - **bindRoleForClick** – `{boolean}` – Adds role=button to non-interactive elements (such as
   *    `div` or `li`) using ng-click, making them more accessible to users of assistive
   *    technologies
   *
   * @description
   * Enables/disables various ARIA attributes
   */
  this.config = function(newConfig) {
    config = angular.extend(config, newConfig);
  };

  function watchExpr(attrName, ariaAttr, nodeBlackList, negate) {
    return function(scope, elem, attr) {
      var ariaCamelName = attr.$normalize(ariaAttr);
      if (config[ariaCamelName] && !isNodeOneOf(elem, nodeBlackList) && !attr[ariaCamelName]) {
        scope.$watch(attr[attrName], function(boolVal) {
          // ensure boolean value
          boolVal = negate ? !boolVal : !!boolVal;
          elem.attr(ariaAttr, boolVal);
        });
      }
    };
  }
  /**
   * @ngdoc service
   * @name $aria
   *
   * @description
   * @priority 200
   *
   * The $aria service contains helper methods for applying common
   * [ARIA](http://www.w3.org/TR/wai-aria/) attributes to HTML directives.
   *
   * ngAria injects common accessibility attributes that tell assistive technologies when HTML
   * elements are enabled, selected, hidden, and more. To see how this is performed with ngAria,
   * let's review a code snippet from ngAria itself:
   *
   *```js
   * ngAriaModule.directive('ngDisabled', ['$aria', function($aria) {
   *   return $aria.$$watchExpr('ngDisabled', 'aria-disabled', nodeBlackList, false);
   * }])
   *```
   * Shown above, the ngAria module creates a directive with the same signature as the
   * traditional `ng-disabled` directive. But this ngAria version is dedicated to
   * solely managing accessibility attributes on custom elements. The internal `$aria` service is
   * used to watch the boolean attribute `ngDisabled`. If it has not been explicitly set by the
   * developer, `aria-disabled` is injected as an attribute with its value synchronized to the
   * value in `ngDisabled`.
   *
   * Because ngAria hooks into the `ng-disabled` directive, developers do not have to do
   * anything to enable this feature. The `aria-disabled` attribute is automatically managed
   * simply as a silent side-effect of using `ng-disabled` with the ngAria module.
   *
   * The full list of directives that interface with ngAria:
   * * **ngModel**
   * * **ngChecked**
   * * **ngReadonly**
   * * **ngRequired**
   * * **ngDisabled**
   * * **ngValue**
   * * **ngShow**
   * * **ngHide**
   * * **ngClick**
   * * **ngDblclick**
   * * **ngMessages**
   *
   * Read the {@link guide/accessibility ngAria Developer Guide} for a thorough explanation of each
   * directive.
   *
   *
   * ## Dependencies
   * Requires the {@link ngAria} module to be installed.
   */
  this.$get = function() {
    return {
      config: function(key) {
        return config[key];
      },
      $$watchExpr: watchExpr
    };
  };
}


ngAriaModule.directive('ngShow', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngShow', 'aria-hidden', [], true);
}])
.directive('ngHide', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngHide', 'aria-hidden', [], false);
}])
.directive('ngValue', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngValue', 'aria-checked', nodeBlackList, false);
}])
.directive('ngChecked', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngChecked', 'aria-checked', nodeBlackList, false);
}])
.directive('ngReadonly', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngReadonly', 'aria-readonly', nodeBlackList, false);
}])
.directive('ngRequired', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngRequired', 'aria-required', nodeBlackList, false);
}])
.directive('ngModel', ['$aria', function($aria) {

  function shouldAttachAttr(attr, normalizedAttr, elem, allowBlacklistEls) {
    return $aria.config(normalizedAttr) && !elem.attr(attr) && (allowBlacklistEls || !isNodeOneOf(elem, nodeBlackList));
  }

  function shouldAttachRole(role, elem) {
    // if element does not have role attribute
    // AND element type is equal to role (if custom element has a type equaling shape) <-- remove?
    // AND element is not in nodeBlackList
    return !elem.attr('role') && (elem.attr('type') === role) && !isNodeOneOf(elem, nodeBlackList);
  }

  function getShape(attr, elem) {
    var type = attr.type,
        role = attr.role;

    return ((type || role) === 'checkbox' || role === 'menuitemcheckbox') ? 'checkbox' :
           ((type || role) === 'radio'    || role === 'menuitemradio') ? 'radio' :
           (type === 'range'              || role === 'progressbar' || role === 'slider') ? 'range' : '';
  }

  return {
    restrict: 'A',
    require: 'ngModel',
    priority: 200, //Make sure watches are fired after any other directives that affect the ngModel value
    compile: function(elem, attr) {
      var shape = getShape(attr, elem);

      return {
        post: function(scope, elem, attr, ngModel) {
          var needsTabIndex = shouldAttachAttr('tabindex', 'tabindex', elem, false);

          function ngAriaWatchModelValue() {
            return ngModel.$modelValue;
          }

          function getRadioReaction(newVal) {
            // Strict comparison would cause a BC
            // eslint-disable-next-line eqeqeq
            var boolVal = (attr.value == ngModel.$viewValue);
            elem.attr('aria-checked', boolVal);
          }

          function getCheckboxReaction() {
            elem.attr('aria-checked', !ngModel.$isEmpty(ngModel.$viewValue));
          }

          switch (shape) {
            case 'radio':
            case 'checkbox':
              if (shouldAttachRole(shape, elem)) {
                elem.attr('role', shape);
              }
              if (shouldAttachAttr('aria-checked', 'ariaChecked', elem, false)) {
                scope.$watch(ngAriaWatchModelValue, shape === 'radio' ?
                    getRadioReaction : getCheckboxReaction);
              }
              if (needsTabIndex) {
                elem.attr('tabindex', 0);
              }
              break;
            case 'range':
              if (shouldAttachRole(shape, elem)) {
                elem.attr('role', 'slider');
              }
              if ($aria.config('ariaValue')) {
                var needsAriaValuemin = !elem.attr('aria-valuemin') &&
                    (attr.hasOwnProperty('min') || attr.hasOwnProperty('ngMin'));
                var needsAriaValuemax = !elem.attr('aria-valuemax') &&
                    (attr.hasOwnProperty('max') || attr.hasOwnProperty('ngMax'));
                var needsAriaValuenow = !elem.attr('aria-valuenow');

                if (needsAriaValuemin) {
                  attr.$observe('min', function ngAriaValueMinReaction(newVal) {
                    elem.attr('aria-valuemin', newVal);
                  });
                }
                if (needsAriaValuemax) {
                  attr.$observe('max', function ngAriaValueMinReaction(newVal) {
                    elem.attr('aria-valuemax', newVal);
                  });
                }
                if (needsAriaValuenow) {
                  scope.$watch(ngAriaWatchModelValue, function ngAriaValueNowReaction(newVal) {
                    elem.attr('aria-valuenow', newVal);
                  });
                }
              }
              if (needsTabIndex) {
                elem.attr('tabindex', 0);
              }
              break;
          }

          if (!attr.hasOwnProperty('ngRequired') && ngModel.$validators.required
            && shouldAttachAttr('aria-required', 'ariaRequired', elem, false)) {
            // ngModel.$error.required is undefined on custom controls
            attr.$observe('required', function() {
              elem.attr('aria-required', !!attr['required']);
            });
          }

          if (shouldAttachAttr('aria-invalid', 'ariaInvalid', elem, true)) {
            scope.$watch(function ngAriaInvalidWatch() {
              return ngModel.$invalid;
            }, function ngAriaInvalidReaction(newVal) {
              elem.attr('aria-invalid', !!newVal);
            });
          }
        }
      };
    }
  };
}])
.directive('ngDisabled', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngDisabled', 'aria-disabled', nodeBlackList, false);
}])
.directive('ngMessages', function() {
  return {
    restrict: 'A',
    require: '?ngMessages',
    link: function(scope, elem, attr, ngMessages) {
      if (!elem.attr('aria-live')) {
        elem.attr('aria-live', 'assertive');
      }
    }
  };
})
.directive('ngClick',['$aria', '$parse', function($aria, $parse) {
  return {
    restrict: 'A',
    compile: function(elem, attr) {
      var fn = $parse(attr.ngClick);
      return function(scope, elem, attr) {

        if (!isNodeOneOf(elem, nodeBlackList)) {

          if ($aria.config('bindRoleForClick') && !elem.attr('role')) {
            elem.attr('role', 'button');
          }

          if ($aria.config('tabindex') && !elem.attr('tabindex')) {
            elem.attr('tabindex', 0);
          }

          if ($aria.config('bindKeydown') && !attr.ngKeydown && !attr.ngKeypress && !attr.ngKeyup) {
            elem.on('keydown', function(event) {
              var keyCode = event.which || event.keyCode;
              if (keyCode === 32 || keyCode === 13) {
                scope.$apply(callback);
              }

              function callback() {
                fn(scope, { $event: event });
              }
            });
          }
        }
      };
    }
  };
}])
.directive('ngDblclick', ['$aria', function($aria) {
  return function(scope, elem, attr) {
    if ($aria.config('tabindex') && !elem.attr('tabindex') && !isNodeOneOf(elem, nodeBlackList)) {
      elem.attr('tabindex', 0);
    }
  };
}]);


})(window, window.angular);

(function(root, factory) {
    /* istanbul ignore next */
    if (typeof define === 'function' && define.amd) {
        // AMD. Register as an anonymous module.
        define([], factory);
    } else if (typeof exports === 'object') {
        // Node. Does not work with strict CommonJS, but
        // only CommonJS-like environments that support module.exports,
        // like Node.
        module.exports = factory();
    } else {
        // Browser globals (root is window)
        root.StringMask = factory();
    }
}(this, function() {
    var tokens = {
        '0': {pattern: /\d/, _default: '0'},
        '9': {pattern: /\d/, optional: true},
        '#': {pattern: /\d/, optional: true, recursive: true},
        'A': {pattern: /[a-zA-Z0-9]/},
        'S': {pattern: /[a-zA-Z]/},
        'U': {pattern: /[a-zA-Z]/, transform: function(c) { return c.toLocaleUpperCase(); }},
        'L': {pattern: /[a-zA-Z]/, transform: function(c) { return c.toLocaleLowerCase(); }},
        '$': {escape: true}
    };

    function isEscaped(pattern, pos) {
        var count = 0;
        var i = pos - 1;
        var token = {escape: true};
        while (i >= 0 && token && token.escape) {
            token = tokens[pattern.charAt(i)];
            count += token && token.escape ? 1 : 0;
            i--;
        }
        return count > 0 && count % 2 === 1;
    }

    function calcOptionalNumbersToUse(pattern, value) {
        var numbersInP = pattern.replace(/[^0]/g,'').length;
        var numbersInV = value.replace(/[^\d]/g,'').length;
        return numbersInV - numbersInP;
    }

    function concatChar(text, character, options, token) {
        if (token && typeof token.transform === 'function') {
            character = token.transform(character);
        }
        if (options.reverse) {
            return character + text;
        }
        return text + character;
    }

    function hasMoreTokens(pattern, pos, inc) {
        var pc = pattern.charAt(pos);
        var token = tokens[pc];
        if (pc === '') {
            return false;
        }
        return token && !token.escape ? true : hasMoreTokens(pattern, pos + inc, inc);
    }

    function hasMoreRecursiveTokens(pattern, pos, inc) {
        var pc = pattern.charAt(pos);
        var token = tokens[pc];
        if (pc === '') {
            return false;
        }
        return token && token.recursive ? true : hasMoreRecursiveTokens(pattern, pos + inc, inc);
    }

    function insertChar(text, char, position) {
        var t = text.split('');
        t.splice(position, 0, char);
        return t.join('');
    }

    function StringMask(pattern, opt) {
        this.options = opt || {};
        this.options = {
            reverse: this.options.reverse || false,
            usedefaults: this.options.usedefaults || this.options.reverse
        };
        this.pattern = pattern;
    }

    StringMask.prototype.process = function proccess(value) {
        if (!value) {
            return {result: '', valid: false};
        }
        value = value + '';
        var pattern2 = this.pattern;
        var valid = true;
        var formatted = '';
        var valuePos = this.options.reverse ? value.length - 1 : 0;
        var patternPos = 0;
        var optionalNumbersToUse = calcOptionalNumbersToUse(pattern2, value);
        var escapeNext = false;
        var recursive = [];
        var inRecursiveMode = false;

        var steps = {
            start: this.options.reverse ? pattern2.length - 1 : 0,
            end: this.options.reverse ? -1 : pattern2.length,
            inc: this.options.reverse ? -1 : 1
        };

        function continueCondition(options) {
            if (!inRecursiveMode && !recursive.length && hasMoreTokens(pattern2, patternPos, steps.inc)) {
                // continue in the normal iteration
                return true;
            } else if (!inRecursiveMode && recursive.length &&
                hasMoreRecursiveTokens(pattern2, patternPos, steps.inc)) {
                // continue looking for the recursive tokens
                // Note: all chars in the patterns after the recursive portion will be handled as static string
                return true;
            } else if (!inRecursiveMode) {
                // start to handle the recursive portion of the pattern
                inRecursiveMode = recursive.length > 0;
            }

            if (inRecursiveMode) {
                var pc = recursive.shift();
                recursive.push(pc);
                if (options.reverse && valuePos >= 0) {
                    patternPos++;
                    pattern2 = insertChar(pattern2, pc, patternPos);
                    return true;
                } else if (!options.reverse && valuePos < value.length) {
                    pattern2 = insertChar(pattern2, pc, patternPos);
                    return true;
                }
            }
            return patternPos < pattern2.length && patternPos >= 0;
        }

        /**
         * Iterate over the pattern's chars parsing/matching the input value chars
         * until the end of the pattern. If the pattern ends with recursive chars
         * the iteration will continue until the end of the input value.
         *
         * Note: The iteration must stop if an invalid char is found.
         */
        for (patternPos = steps.start; continueCondition(this.options); patternPos = patternPos + steps.inc) {
            // Value char
            var vc = value.charAt(valuePos);
            // Pattern char to match with the value char
            var pc = pattern2.charAt(patternPos);

            var token = tokens[pc];
            if (recursive.length && token && !token.recursive) {
                // In the recursive portion of the pattern: tokens not recursive must be seen as static chars
                token = null;
            }

            // 1. Handle escape tokens in pattern
            // go to next iteration: if the pattern char is a escape char or was escaped
            if (!inRecursiveMode || vc) {
                if (this.options.reverse && isEscaped(pattern2, patternPos)) {
                    // pattern char is escaped, just add it and move on
                    formatted = concatChar(formatted, pc, this.options, token);
                    // skip escape token
                    patternPos = patternPos + steps.inc;
                    continue;
                } else if (!this.options.reverse && escapeNext) {
                    // pattern char is escaped, just add it and move on
                    formatted = concatChar(formatted, pc, this.options, token);
                    escapeNext = false;
                    continue;
                } else if (!this.options.reverse && token && token.escape) {
                    // mark to escape the next pattern char
                    escapeNext = true;
                    continue;
                }
            }

            // 2. Handle recursive tokens in pattern
            // go to next iteration: if the value str is finished or
            //                       if there is a normal token in the recursive portion of the pattern
            if (!inRecursiveMode && token && token.recursive) {
                // save it to repeat in the end of the pattern and handle the value char now
                recursive.push(pc);
            } else if (inRecursiveMode && !vc) {
                // in recursive mode but value is finished. Add the pattern char if it is not a recursive token
                formatted = concatChar(formatted, pc, this.options, token);
                continue;
            } else if (!inRecursiveMode && recursive.length > 0 && !vc) {
                // recursiveMode not started but already in the recursive portion of the pattern
                continue;
            }

            // 3. Handle the value
            // break iterations: if value is invalid for the given pattern
            if (!token) {
                // add char of the pattern
                formatted = concatChar(formatted, pc, this.options, token);
                if (!inRecursiveMode && recursive.length) {
                    // save it to repeat in the end of the pattern
                    recursive.push(pc);
                }
            } else if (token.optional) {
                // if token is optional, only add the value char if it matchs the token pattern
                //                       if not, move on to the next pattern char
                if (token.pattern.test(vc) && optionalNumbersToUse) {
                    formatted = concatChar(formatted, vc, this.options, token);
                    valuePos = valuePos + steps.inc;
                    optionalNumbersToUse--;
                } else if (recursive.length > 0 && vc) {
                    valid = false;
                    break;
                }
            } else if (token.pattern.test(vc)) {
                // if token isn't optional the value char must match the token pattern
                formatted = concatChar(formatted, vc, this.options, token);
                valuePos = valuePos + steps.inc;
            } else if (!vc && token._default && this.options.usedefaults) {
                // if the token isn't optional and has a default value, use it if the value is finished
                formatted = concatChar(formatted, token._default, this.options, token);
            } else {
                // the string value don't match the given pattern
                valid = false;
                break;
            }
        }

        return {result: formatted, valid: valid};
    };

    StringMask.prototype.apply = function(value) {
        return this.process(value).result;
    };

    StringMask.prototype.validate = function(value) {
        return this.process(value).valid;
    };

    StringMask.process = function(value, pattern, options) {
        return new StringMask(pattern, options).process(value);
    };

    StringMask.apply = function(value, pattern, options) {
        return new StringMask(pattern, options).apply(value);
    };

    StringMask.validate = function(value, pattern, options) {
        return new StringMask(pattern, options).validate(value);
    };

    return StringMask;
}));

/**
 * br-masks
 * A library of masks applicable to several Brazilian data like I.E., CNPJ, CPF and others
 * @version v0.5.0
 * @link http://github.com/the-darc/br-masks
 * @license MIT
 */
(function (root, factory) {
   /* istanbul ignore next */
	if (typeof define === 'function' && define.amd) {
		// AMD. Register as an anonymous module.
		define(['string-mask'], factory);
	} else if (typeof exports === 'object') {
		// Node. Does not work with strict CommonJS, but
		// only CommonJS-like environments that support module.exports,
		// like Node.
		module.exports = factory(require('string-mask'));
	} else {
		// Browser globals (root is window)
		root.BrM = factory(root.StringMask);
	}
}(this, function (StringMask) {
   /* istanbul ignore if */
	if (!StringMask) {
		throw new Error('StringMask not found');
	}
/*exported CEP */
var CEP = function(value) {
	var cepMask = new StringMask('00000-000');
	if(!value) {
		return value;
	}
	var processed = cepMask.process(value);
	return processed.result;
};

/*exported CNPJBASE */
var CNPJBASE = function(value) {
	if(!value) {
		return value;
	}
	var cnpjBasePattern = new StringMask('00.000.000');
	var formatedValue = cnpjBasePattern.apply(value);
	return formatedValue;
};

/*exported CNPJ */
var CNPJ = function(value) {
	if(!value) {
		return value;
	}
	var cnpjPattern = new StringMask('00.000.000\/0000-00');
	var formatedValue = cnpjPattern.apply(value);
	return formatedValue;
};

/*exported CPFCNPJ */
/*globals CPF, CNPJ*/
var CPFCNPJ = function(value) {
	if (!value || !value.length) {
		return value;
	} else if (value.length <= 11) {
		return CPF(value);
	} else {
		return CNPJ(value);
	}
};

/*exported CPF */
var CPF = function(value) {
	var cpfPattern = new StringMask('000.000.000-00');
	if(!value) {
		return value;
	}
	var formatedValue = cpfPattern.apply(value);
	return formatedValue;
};

/*exported FINANCE */
var FINANCE = function(value, precision, decimalSep, groupSep) {
	precision = (!precision && precision !== 0) || precision < 0? 2 : precision;
	decimalSep = decimalSep || '.';
	groupSep = groupSep || '';

	var decimalsPattern = precision > 0 ? decimalSep + new Array(precision + 1).join('0') : '';
	var maskPattern = '#'+groupSep+'##0'+decimalsPattern;

	value = parseFloat(value);
	if (!value) {
		value = 0;
	}

	var negative = false;
	if (value < 0) {
		value = value * -1;
		negative = true;
	}
	var financeMask = new StringMask(maskPattern, {reverse: true});
	var masked = financeMask.apply(value.toFixed(precision).replace(/[^\d]+/g,''));
	return negative ? '('+masked+')' : masked;
};

/*exported IE */
var IE = function(value, uf) {
	if (!value || typeof value !== 'string') {
		return value;
	}

	var ieMasks = {
		'AC': [{mask: new StringMask('00.000.000/000-00')}],
		'AL': [{mask: new StringMask('000000000')}],
		'AM': [{mask: new StringMask('00.000.000-0')}],
		'AP': [{mask: new StringMask('000000000')}],
		'BA': [{chars: 8, mask: new StringMask('000000-00')},
			   {mask: new StringMask('0000000-00')}],
		'CE': [{mask: new StringMask('00000000-0')}],
		'DF': [{mask: new StringMask('00000000000-00')}],
		'ES': [{mask: new StringMask('00000000-0')}],
		'GO': [{mask: new StringMask('00.000.000-0')}],
		'MA': [{mask: new StringMask('000000000')}],
		'MG': [{mask: new StringMask('000.000.000/0000')}],
		'MS': [{mask: new StringMask('000000000')}],
		'MT': [{mask: new StringMask('0000000000-0')}],
		'PA': [{mask: new StringMask('00-000000-0')}],
		'PB': [{mask: new StringMask('00000000-0')}],
		'PE': [{chars: 9, mask: new StringMask('0000000-00')},
			   {mask: new StringMask('00.0.000.0000000-0')}],
		'PI': [{mask: new StringMask('000000000')}],
		'PR': [{mask: new StringMask('000.00000-00')}],
		'RJ': [{mask: new StringMask('00.000.00-0')}],
		'RN': [{chars: 9, mask: new StringMask('00.000.000-0')},
			   {mask: new StringMask('00.0.000.000-0')}],
		'RO': [{mask: new StringMask('0000000000000-0')}],
		'RR': [{mask: new StringMask('00000000-0')}],
		'RS': [{mask: new StringMask('000/0000000')}],
		'SC': [{mask: new StringMask('000.000.000')}],
		'SE': [{mask: new StringMask('00000000-0')}],
		'SP': [{mask: new StringMask('000.000.000.000')},
			   {mask: new StringMask('-00000000.0/000')}],
		'TO': [{mask: new StringMask('00000000000')}]
	};

	function clearValue (value) {
		return value.replace(/[^0-9]/g, '');
	}

	function getMask(uf, value) {
		if(!uf || !ieMasks[uf]) {
			return undefined;
		}
		var _uf = uf.toUpperCase();
		if (_uf === 'SP' && /^P/i.test(value)) {
			return ieMasks.SP[1].mask;
		}
		var masks = ieMasks[uf];
		var i = 0;
		while(masks[i].chars && masks[i].chars < clearValue(value).length && i < masks.length - 1) {
			i++;
		}
		return masks[i].mask;
	}

	var mask = getMask(uf, value);
	if(!mask) {
		return value;
	}
	var processed = mask.process(clearValue(value));
	if (uf && uf.toUpperCase() === 'SP' && /^p/i.test(value)) {
		return 'P'+processed.result;
	}
	return processed.result;
};

/*exported NFEACCESSKEY */
var NFEACCESSKEY = function(value) {
	if(!value) {
		return value;
	}

	var maskPattern = '0000 0000 0000 0000 0000 0000 0000 0000 0000 0000 0000';
	var nfeMask = new StringMask(maskPattern);
	var formatedValue = nfeMask.apply(value);
	return formatedValue;
};

/*exported PHONE */
var PHONE = function(value) {
	var phoneMask8D = new StringMask('(00) 0000-0000'),
		phoneMask9D = new StringMask('(00) 00000-0000'),
		phoneMask0800 = new StringMask('0000-000-0000');

	if(!value) {
		return value;
	}

	var formatedValue;
	value = value + '';
	if (value.indexOf('0800') === 0) {
			formatedValue = phoneMask0800.apply(value);
	}else if(value.length < 11){
		formatedValue = phoneMask8D.apply(value);
	}else{
		formatedValue = phoneMask9D.apply(value);
	}

	return formatedValue;
};

	return {
		ie: IE,
		cpf: CPF,
		cnpj: CNPJ,
       cnpjBase: CNPJBASE,
		phone: PHONE,
		cep: CEP,
		finance: FINANCE,
		nfeAccessKey: NFEACCESSKEY,
		cpfCnpj: CPFCNPJ
	};
}));
/**
 * angular-br-filters
 * An Angular library of masks applicable to several Brazilian data.
 * @version v0.6.0
 * @link https://github.com/the-darc/angular-br-filters
 * @license MIT
 */(function e(t,n,r){function s(o,u){if(!n[o]){if(!t[o]){var a=typeof require=="function"&&require;if(!u&&a)return a(o,!0);if(i)return i(o,!0);var f=new Error("Cannot find module '"+o+"'");throw f.code="MODULE_NOT_FOUND",f}var l=n[o]={exports:{}};t[o][0].call(l.exports,function(e){var n=t[o][1][e];return s(n?n:e)},l,l.exports,e,t,n,r)}return n[o].exports}var i=typeof require=="function"&&require;for(var o=0;o<r.length;o++)s(r[o]);return s})({1:[function(require,module,exports){
(function(root, factory) {
    /* istanbul ignore next */
    if (typeof define === 'function' && define.amd) {
        // AMD. Register as an anonymous module.
        define([], factory);
    } else if (typeof exports === 'object') {
        // Node. Does not work with strict CommonJS, but
        // only CommonJS-like environments that support module.exports,
        // like Node.
        module.exports = factory();
    } else {
        // Browser globals (root is window)
        root.StringMask = factory();
    }
}(this, function() {
    var tokens = {
        '0': {pattern: /\d/, _default: '0'},
        '9': {pattern: /\d/, optional: true},
        '#': {pattern: /\d/, optional: true, recursive: true},
        'A': {pattern: /[a-zA-Z0-9]/},
        'S': {pattern: /[a-zA-Z]/},
        'U': {pattern: /[a-zA-Z]/, transform: function(c) { return c.toLocaleUpperCase(); }},
        'L': {pattern: /[a-zA-Z]/, transform: function(c) { return c.toLocaleLowerCase(); }},
        '$': {escape: true}
    };

    function isEscaped(pattern, pos) {
        var count = 0;
        var i = pos - 1;
        var token = {escape: true};
        while (i >= 0 && token && token.escape) {
            token = tokens[pattern.charAt(i)];
            count += token && token.escape ? 1 : 0;
            i--;
        }
        return count > 0 && count % 2 === 1;
    }

    function calcOptionalNumbersToUse(pattern, value) {
        var numbersInP = pattern.replace(/[^0]/g,'').length;
        var numbersInV = value.replace(/[^\d]/g,'').length;
        return numbersInV - numbersInP;
    }

    function concatChar(text, character, options, token) {
        if (token && typeof token.transform === 'function') {
            character = token.transform(character);
        }
        if (options.reverse) {
            return character + text;
        }
        return text + character;
    }

    function hasMoreTokens(pattern, pos, inc) {
        var pc = pattern.charAt(pos);
        var token = tokens[pc];
        if (pc === '') {
            return false;
        }
        return token && !token.escape ? true : hasMoreTokens(pattern, pos + inc, inc);
    }

    function hasMoreRecursiveTokens(pattern, pos, inc) {
        var pc = pattern.charAt(pos);
        var token = tokens[pc];
        if (pc === '') {
            return false;
        }
        return token && token.recursive ? true : hasMoreRecursiveTokens(pattern, pos + inc, inc);
    }

    function insertChar(text, char, position) {
        var t = text.split('');
        t.splice(position, 0, char);
        return t.join('');
    }

    function StringMask(pattern, opt) {
        this.options = opt || {};
        this.options = {
            reverse: this.options.reverse || false,
            usedefaults: this.options.usedefaults || this.options.reverse
        };
        this.pattern = pattern;
    }

    StringMask.prototype.process = function proccess(value) {
        if (!value) {
            return {result: '', valid: false};
        }
        value = value + '';
        var pattern2 = this.pattern;
        var valid = true;
        var formatted = '';
        var valuePos = this.options.reverse ? value.length - 1 : 0;
        var patternPos = 0;
        var optionalNumbersToUse = calcOptionalNumbersToUse(pattern2, value);
        var escapeNext = false;
        var recursive = [];
        var inRecursiveMode = false;

        var steps = {
            start: this.options.reverse ? pattern2.length - 1 : 0,
            end: this.options.reverse ? -1 : pattern2.length,
            inc: this.options.reverse ? -1 : 1
        };

        function continueCondition(options) {
            if (!inRecursiveMode && !recursive.length && hasMoreTokens(pattern2, patternPos, steps.inc)) {
                // continue in the normal iteration
                return true;
            } else if (!inRecursiveMode && recursive.length &&
                hasMoreRecursiveTokens(pattern2, patternPos, steps.inc)) {
                // continue looking for the recursive tokens
                // Note: all chars in the patterns after the recursive portion will be handled as static string
                return true;
            } else if (!inRecursiveMode) {
                // start to handle the recursive portion of the pattern
                inRecursiveMode = recursive.length > 0;
            }

            if (inRecursiveMode) {
                var pc = recursive.shift();
                recursive.push(pc);
                if (options.reverse && valuePos >= 0) {
                    patternPos++;
                    pattern2 = insertChar(pattern2, pc, patternPos);
                    return true;
                } else if (!options.reverse && valuePos < value.length) {
                    pattern2 = insertChar(pattern2, pc, patternPos);
                    return true;
                }
            }
            return patternPos < pattern2.length && patternPos >= 0;
        }

        /**
         * Iterate over the pattern's chars parsing/matching the input value chars
         * until the end of the pattern. If the pattern ends with recursive chars
         * the iteration will continue until the end of the input value.
         *
         * Note: The iteration must stop if an invalid char is found.
         */
        for (patternPos = steps.start; continueCondition(this.options); patternPos = patternPos + steps.inc) {
            // Value char
            var vc = value.charAt(valuePos);
            // Pattern char to match with the value char
            var pc = pattern2.charAt(patternPos);

            var token = tokens[pc];
            if (recursive.length && token && !token.recursive) {
                // In the recursive portion of the pattern: tokens not recursive must be seen as static chars
                token = null;
            }

            // 1. Handle escape tokens in pattern
            // go to next iteration: if the pattern char is a escape char or was escaped
            if (!inRecursiveMode || vc) {
                if (this.options.reverse && isEscaped(pattern2, patternPos)) {
                    // pattern char is escaped, just add it and move on
                    formatted = concatChar(formatted, pc, this.options, token);
                    // skip escape token
                    patternPos = patternPos + steps.inc;
                    continue;
                } else if (!this.options.reverse && escapeNext) {
                    // pattern char is escaped, just add it and move on
                    formatted = concatChar(formatted, pc, this.options, token);
                    escapeNext = false;
                    continue;
                } else if (!this.options.reverse && token && token.escape) {
                    // mark to escape the next pattern char
                    escapeNext = true;
                    continue;
                }
            }

            // 2. Handle recursive tokens in pattern
            // go to next iteration: if the value str is finished or
            //                       if there is a normal token in the recursive portion of the pattern
            if (!inRecursiveMode && token && token.recursive) {
                // save it to repeat in the end of the pattern and handle the value char now
                recursive.push(pc);
            } else if (inRecursiveMode && !vc) {
                // in recursive mode but value is finished. Add the pattern char if it is not a recursive token
                formatted = concatChar(formatted, pc, this.options, token);
                continue;
            } else if (!inRecursiveMode && recursive.length > 0 && !vc) {
                // recursiveMode not started but already in the recursive portion of the pattern
                continue;
            }

            // 3. Handle the value
            // break iterations: if value is invalid for the given pattern
            if (!token) {
                // add char of the pattern
                formatted = concatChar(formatted, pc, this.options, token);
                if (!inRecursiveMode && recursive.length) {
                    // save it to repeat in the end of the pattern
                    recursive.push(pc);
                }
            } else if (token.optional) {
                // if token is optional, only add the value char if it matchs the token pattern
                //                       if not, move on to the next pattern char
                if (token.pattern.test(vc) && optionalNumbersToUse) {
                    formatted = concatChar(formatted, vc, this.options, token);
                    valuePos = valuePos + steps.inc;
                    optionalNumbersToUse--;
                } else if (recursive.length > 0 && vc) {
                    valid = false;
                    break;
                }
            } else if (token.pattern.test(vc)) {
                // if token isn't optional the value char must match the token pattern
                formatted = concatChar(formatted, vc, this.options, token);
                valuePos = valuePos + steps.inc;
            } else if (!vc && token._default && this.options.usedefaults) {
                // if the token isn't optional and has a default value, use it if the value is finished
                formatted = concatChar(formatted, token._default, this.options, token);
            } else {
                // the string value don't match the given pattern
                valid = false;
                break;
            }
        }

        return {result: formatted, valid: valid};
    };

    StringMask.prototype.apply = function(value) {
        return this.process(value).result;
    };

    StringMask.prototype.validate = function(value) {
        return this.process(value).valid;
    };

    StringMask.process = function(value, pattern, options) {
        return new StringMask(pattern, options).process(value);
    };

    StringMask.apply = function(value, pattern, options) {
        return new StringMask(pattern, options).apply(value);
    };

    StringMask.validate = function(value, pattern, options) {
        return new StringMask(pattern, options).validate(value);
    };

    return StringMask;
}));

},{}],2:[function(require,module,exports){
/**
 * br-masks
 * A library of masks applicable to several Brazilian data like I.E., CNPJ, CPF and others
 * @version v0.5.0
 * @link http://github.com/the-darc/br-masks
 * @license MIT
 */
(function (root, factory) {
   /* istanbul ignore next */
	if (typeof define === 'function' && define.amd) {
		// AMD. Register as an anonymous module.
		define(['string-mask'], factory);
	} else if (typeof exports === 'object') {
		// Node. Does not work with strict CommonJS, but
		// only CommonJS-like environments that support module.exports,
		// like Node.
		module.exports = factory(require('string-mask'));
	} else {
		// Browser globals (root is window)
		root.BrM = factory(root.StringMask);
	}
}(this, function (StringMask) {
   /* istanbul ignore if */
	if (!StringMask) {
		throw new Error('StringMask not found');
	}
/*exported CEP */
var CEP = function(value) {
	var cepMask = new StringMask('00000-000');
	if(!value) {
		return value;
	}
	var processed = cepMask.process(value);
	return processed.result;
};

/*exported CNPJBASE */
var CNPJBASE = function(value) {
	if(!value) {
		return value;
	}
	var cnpjBasePattern = new StringMask('00.000.000');
	var formatedValue = cnpjBasePattern.apply(value);
	return formatedValue;
};

/*exported CNPJ */
var CNPJ = function(value) {
	if(!value) {
		return value;
	}
	var cnpjPattern = new StringMask('00.000.000\/0000-00');
	var formatedValue = cnpjPattern.apply(value);
	return formatedValue;
};

/*exported CPFCNPJ */
/*globals CPF, CNPJ*/
var CPFCNPJ = function(value) {
	if (!value || !value.length) {
		return value;
	} else if (value.length <= 11) {
		return CPF(value);
	} else {
		return CNPJ(value);
	}
};

/*exported CPF */
var CPF = function(value) {
	var cpfPattern = new StringMask('000.000.000-00');
	if(!value) {
		return value;
	}
	var formatedValue = cpfPattern.apply(value);
	return formatedValue;
};

/*exported FINANCE */
var FINANCE = function(value, precision, decimalSep, groupSep) {
	precision = (!precision && precision !== 0) || precision < 0? 2 : precision;
	decimalSep = decimalSep || '.';
	groupSep = groupSep || '';

	var decimalsPattern = precision > 0 ? decimalSep + new Array(precision + 1).join('0') : '';
	var maskPattern = '#'+groupSep+'##0'+decimalsPattern;

	value = parseFloat(value);
	if (!value) {
		value = 0;
	}

	var negative = false;
	if (value < 0) {
		value = value * -1;
		negative = true;
	}
	var financeMask = new StringMask(maskPattern, {reverse: true});
	var masked = financeMask.apply(value.toFixed(precision).replace(/[^\d]+/g,''));
	return negative ? '('+masked+')' : masked;
};

/*exported IE */
var IE = function(value, uf) {
	if (!value || typeof value !== 'string') {
		return value;
	}

	var ieMasks = {
		'AC': [{mask: new StringMask('00.000.000/000-00')}],
		'AL': [{mask: new StringMask('000000000')}],
		'AM': [{mask: new StringMask('00.000.000-0')}],
		'AP': [{mask: new StringMask('000000000')}],
		'BA': [{chars: 8, mask: new StringMask('000000-00')},
			   {mask: new StringMask('0000000-00')}],
		'CE': [{mask: new StringMask('00000000-0')}],
		'DF': [{mask: new StringMask('00000000000-00')}],
		'ES': [{mask: new StringMask('00000000-0')}],
		'GO': [{mask: new StringMask('00.000.000-0')}],
		'MA': [{mask: new StringMask('000000000')}],
		'MG': [{mask: new StringMask('000.000.000/0000')}],
		'MS': [{mask: new StringMask('000000000')}],
		'MT': [{mask: new StringMask('0000000000-0')}],
		'PA': [{mask: new StringMask('00-000000-0')}],
		'PB': [{mask: new StringMask('00000000-0')}],
		'PE': [{chars: 9, mask: new StringMask('0000000-00')},
			   {mask: new StringMask('00.0.000.0000000-0')}],
		'PI': [{mask: new StringMask('000000000')}],
		'PR': [{mask: new StringMask('000.00000-00')}],
		'RJ': [{mask: new StringMask('00.000.00-0')}],
		'RN': [{chars: 9, mask: new StringMask('00.000.000-0')},
			   {mask: new StringMask('00.0.000.000-0')}],
		'RO': [{mask: new StringMask('0000000000000-0')}],
		'RR': [{mask: new StringMask('00000000-0')}],
		'RS': [{mask: new StringMask('000/0000000')}],
		'SC': [{mask: new StringMask('000.000.000')}],
		'SE': [{mask: new StringMask('00000000-0')}],
		'SP': [{mask: new StringMask('000.000.000.000')},
			   {mask: new StringMask('-00000000.0/000')}],
		'TO': [{mask: new StringMask('00000000000')}]
	};

	function clearValue (value) {
		return value.replace(/[^0-9]/g, '');
	}

	function getMask(uf, value) {
		if(!uf || !ieMasks[uf]) {
			return undefined;
		}
		var _uf = uf.toUpperCase();
		if (_uf === 'SP' && /^P/i.test(value)) {
			return ieMasks.SP[1].mask;
		}
		var masks = ieMasks[uf];
		var i = 0;
		while(masks[i].chars && masks[i].chars < clearValue(value).length && i < masks.length - 1) {
			i++;
		}
		return masks[i].mask;
	}

	var mask = getMask(uf, value);
	if(!mask) {
		return value;
	}
	var processed = mask.process(clearValue(value));
	if (uf && uf.toUpperCase() === 'SP' && /^p/i.test(value)) {
		return 'P'+processed.result;
	}
	return processed.result;
};

/*exported NFEACCESSKEY */
var NFEACCESSKEY = function(value) {
	if(!value) {
		return value;
	}

	var maskPattern = '0000 0000 0000 0000 0000 0000 0000 0000 0000 0000 0000';
	var nfeMask = new StringMask(maskPattern);
	var formatedValue = nfeMask.apply(value);
	return formatedValue;
};

/*exported PHONE */
var PHONE = function(value) {
	var phoneMask8D = new StringMask('(00) 0000-0000'),
		phoneMask9D = new StringMask('(00) 00000-0000'),
		phoneMask0800 = new StringMask('0000-000-0000');

	if(!value) {
		return value;
	}

	var formatedValue;
	value = value + '';
	if (value.indexOf('0800') === 0) {
			formatedValue = phoneMask0800.apply(value);
	}else if(value.length < 11){
		formatedValue = phoneMask8D.apply(value);
	}else{
		formatedValue = phoneMask9D.apply(value);
	}

	return formatedValue;
};

	return {
		ie: IE,
		cpf: CPF,
		cnpj: CNPJ,
       cnpjBase: CNPJBASE,
		phone: PHONE,
		cep: CEP,
		finance: FINANCE,
		nfeAccessKey: NFEACCESSKEY,
		cpfCnpj: CPFCNPJ
	};
}));
},{"string-mask":1}],3:[function(require,module,exports){
var BrM = require('br-masks');

var m = angular.module('idf.br-filters', []);

module.exports = m.name;

m.filter('percentage', ['$filter', function($filter) {
	return function(input, decimals) {
		if (angular.isUndefined(input) || input === null) {
			return input;
		}

		return $filter('number')(input * 100, decimals) + '%';
	};
}])
.filter('brCep', [function() {
	return function(input) {
		return BrM.cep(input);
	};
}])
.filter('brPhoneNumber', [function() {
	return function(input) {
		return BrM.phone(input);
	};
}])
.filter('brCpf', [function() {
	return function(input) {
		return BrM.cpf(input);
	};
}])
.filter('brCnpj', [function() {
	return function(input) {
		return BrM.cnpj(input);
	};
}])
.filter('brCpfCnpj', [function() {
	return function(input) {
		return BrM.cpfCnpj(input);
	};
}])
.filter('brIe', [function() {
	return function(input, uf) {
		return BrM.ie(input,uf);
	};
}])
.filter('finance', ['$locale', function($locale) {
	return function(input, currency, decimals) {
		if (angular.isUndefined(input) || input === null) {
			return input;
		}

		var decimalDelimiter = $locale.NUMBER_FORMATS.DECIMAL_SEP,
			thousandsDelimiter = $locale.NUMBER_FORMATS.GROUP_SEP,
			currencySym = '';

		if (currency === true) {
			currencySym = $locale.NUMBER_FORMATS.CURRENCY_SYM + ' ';
		} else if (currency) {
			currencySym = currency;
		}

		return currencySym + BrM.finance(input, decimals, decimalDelimiter, thousandsDelimiter);
	};
}])
.filter('nfeAccessKey', [function() {
	return function(input) {
		return BrM.nfeAccessKey(input);
	};
}])
.filter('age', function() {
	/**
	 * @param value birthdate can be a date object or a time in milliseconds
	 * return the age based on the birthdate or undefined if value is invalid.
	 */
	 return function calculateAge(value) {
		 if (!value) {
			 return undefined;
		 }
		 var isDateInstance = (value instanceof Date);
		 var isValidType = isDateInstance || !isNaN(parseInt(value));
		 if (!isValidType) {
			 return undefined;
		 }
		 var birthdate = isDateInstance ? value : new Date(value);
		 if (birthdate > new Date()) {
			 return undefined;
		 }
		 var ageDifMs = Date.now() - birthdate.getTime();
		 var ageDate = new Date(ageDifMs); // miliseconds from epoch
		 return Math.abs(ageDate.getUTCFullYear() - 1970);
	 };
});

},{"br-masks":2}]},{},[3]);

angular.module('ngCacheBuster', [])
  .config(['$httpProvider', function($httpProvider) {
    return $httpProvider.interceptors.push('httpRequestInterceptorCacheBuster');
  }])
    .provider('httpRequestInterceptorCacheBuster', function() {
	
	this.matchlist = [/.*partials.*/, /.*views.*/ ];
	this.logRequests = false;
	
	//Default to whitelist (i.e. block all except matches)
	this.black=false; 
	
	//Select blacklist or whitelist, default to whitelist
	this.setMatchlist = function(list,black) {
	    this.black = typeof black != 'undefined' ? black : false
	    this.matchlist = list;
	};
	

	this.setLogRequests = function(logRequests) {
	    this.logRequests = logRequests;
	};
	
	this.$get = ['$q', '$log', function($q, $log) {
	    var matchlist = this.matchlist;
	    var logRequests = this.logRequests;
	    var black = this.black;
        if (logRequests) {
            $log.log("Blacklist? ",black);
        }
	    return {
		'request': function(config) {
		    //Blacklist by default, match with whitelist
		    var busted= !black; 
		    
		    for(var i=0; i< matchlist.length; i++){
			if(config.url.match(matchlist[i])) {
			    busted=black; break;
			}
		    }
		    
		    //Bust if the URL was on blacklist or not on whitelist
		    if (busted) {
			var d = new Date();
			config.url = config.url.replace(/[?|&]cacheBuster=\d+/,'');
			//Some url's allready have '?' attached
			config.url+=config.url.indexOf('?') === -1 ? '?' : '&'
			config.url += 'cacheBuster=' + d.getTime();
		    }
		    
		    if (logRequests) {
			var log='request.url =' + config.url
			busted ? $log.warn(log) : $log.info(log)
		    }

		    return config || $q.when(config);
		}
	    }
	}];
    });



/**
 * State-based routing for AngularJS 1.x
 * NOTICE: This monolithic bundle also bundles the @uirouter/core code.
 *         This causes it to be incompatible with plugins that depend on @uirouter/core.
 *         We recommend switching to the ui-router-core.js and ui-router-angularjs.js bundles instead.
 *         For more information, see https://ui-router.github.io/blog/uirouter-for-angularjs-umd-bundles
 * @version v1.0.30
 * @link https://ui-router.github.io
 * @license MIT License, http://www.opensource.org/licenses/MIT
 */
(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports, require('angular')) :
    typeof define === 'function' && define.amd ? define(['exports', 'angular'], factory) :
    (global = global || self, factory(global['@uirouter/angularjs'] = {}, global.angular));
}(this, (function (exports, ng_from_import) { 'use strict';

    /** @publicapi @module ng1 */ /** */
    /** @hidden */ var ng_from_global = angular;
    /** @hidden */ var ng = ng_from_import && ng_from_import.module ? ng_from_import : ng_from_global;

    /**
     * Higher order functions
     *
     * These utility functions are exported, but are subject to change without notice.
     *
     * @packageDocumentation
     */
    var __spreadArrays = (undefined && undefined.__spreadArrays) || function () {
        for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
        for (var r = Array(s), k = 0, i = 0; i < il; i++)
            for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
                r[k] = a[j];
        return r;
    };
    /**
     * Returns a new function for [Partial Application](https://en.wikipedia.org/wiki/Partial_application) of the original function.
     *
     * Given a function with N parameters, returns a new function that supports partial application.
     * The new function accepts anywhere from 1 to N parameters.  When that function is called with M parameters,
     * where M is less than N, it returns a new function that accepts the remaining parameters.  It continues to
     * accept more parameters until all N parameters have been supplied.
     *
     *
     * This contrived example uses a partially applied function as an predicate, which returns true
     * if an object is found in both arrays.
     * @example
     * ```
     * // returns true if an object is in both of the two arrays
     * function inBoth(array1, array2, object) {
     *   return array1.indexOf(object) !== -1 &&
     *          array2.indexOf(object) !== 1;
     * }
     * let obj1, obj2, obj3, obj4, obj5, obj6, obj7
     * let foos = [obj1, obj3]
     * let bars = [obj3, obj4, obj5]
     *
     * // A curried "copy" of inBoth
     * let curriedInBoth = curry(inBoth);
     * // Partially apply both the array1 and array2
     * let inFoosAndBars = curriedInBoth(foos, bars);
     *
     * // Supply the final argument; since all arguments are
     * // supplied, the original inBoth function is then called.
     * let obj1InBoth = inFoosAndBars(obj1); // false
     *
     * // Use the inFoosAndBars as a predicate.
     * // Filter, on each iteration, supplies the final argument
     * let allObjs = [ obj1, obj2, obj3, obj4, obj5, obj6, obj7 ];
     * let foundInBoth = allObjs.filter(inFoosAndBars); // [ obj3 ]
     *
     * ```
     *
     * @param fn
     * @returns {*|function(): (*|any)}
     */
    function curry(fn) {
        return function curried() {
            if (arguments.length >= fn.length) {
                return fn.apply(this, arguments);
            }
            var args = Array.prototype.slice.call(arguments);
            return curried.bind.apply(curried, __spreadArrays([this], args));
        };
    }
    /**
     * Given a varargs list of functions, returns a function that composes the argument functions, right-to-left
     * given: f(x), g(x), h(x)
     * let composed = compose(f,g,h)
     * then, composed is: f(g(h(x)))
     */
    function compose() {
        var args = arguments;
        var start = args.length - 1;
        return function () {
            var i = start, result = args[start].apply(this, arguments);
            while (i--)
                result = args[i].call(this, result);
            return result;
        };
    }
    /**
     * Given a varargs list of functions, returns a function that is composes the argument functions, left-to-right
     * given: f(x), g(x), h(x)
     * let piped = pipe(f,g,h);
     * then, piped is: h(g(f(x)))
     */
    function pipe() {
        var funcs = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            funcs[_i] = arguments[_i];
        }
        return compose.apply(null, [].slice.call(arguments).reverse());
    }
    /**
     * Given a property name, returns a function that returns that property from an object
     * let obj = { foo: 1, name: "blarg" };
     * let getName = prop("name");
     * getName(obj) === "blarg"
     */
    var prop = function (name) { return function (obj) { return obj && obj[name]; }; };
    /**
     * Given a property name and a value, returns a function that returns a boolean based on whether
     * the passed object has a property that matches the value
     * let obj = { foo: 1, name: "blarg" };
     * let getName = propEq("name", "blarg");
     * getName(obj) === true
     */
    var propEq = curry(function (name, _val, obj) { return obj && obj[name] === _val; });
    /**
     * Given a dotted property name, returns a function that returns a nested property from an object, or undefined
     * let obj = { id: 1, nestedObj: { foo: 1, name: "blarg" }, };
     * let getName = prop("nestedObj.name");
     * getName(obj) === "blarg"
     * let propNotFound = prop("this.property.doesnt.exist");
     * propNotFound(obj) === undefined
     */
    var parse = function (name) { return pipe.apply(null, name.split('.').map(prop)); };
    /**
     * Given a function that returns a truthy or falsey value, returns a
     * function that returns the opposite (falsey or truthy) value given the same inputs
     */
    var not = function (fn) { return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return !fn.apply(null, args);
    }; };
    /**
     * Given two functions that return truthy or falsey values, returns a function that returns truthy
     * if both functions return truthy for the given arguments
     */
    function and(fn1, fn2) {
        return function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return fn1.apply(null, args) && fn2.apply(null, args);
        };
    }
    /**
     * Given two functions that return truthy or falsey values, returns a function that returns truthy
     * if at least one of the functions returns truthy for the given arguments
     */
    function or(fn1, fn2) {
        return function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return fn1.apply(null, args) || fn2.apply(null, args);
        };
    }
    /**
     * Check if all the elements of an array match a predicate function
     *
     * @param fn1 a predicate function `fn1`
     * @returns a function which takes an array and returns true if `fn1` is true for all elements of the array
     */
    var all = function (fn1) { return function (arr) { return arr.reduce(function (b, x) { return b && !!fn1(x); }, true); }; };
    // tslint:disable-next-line:variable-name
    var any = function (fn1) { return function (arr) { return arr.reduce(function (b, x) { return b || !!fn1(x); }, false); }; };
    /** Given a class, returns a Predicate function that returns true if the object is of that class */
    var is = function (ctor) { return function (obj) {
        return (obj != null && obj.constructor === ctor) || obj instanceof ctor;
    }; };
    /** Given a value, returns a Predicate function that returns true if another value is === equal to the original value */
    var eq = function (value) { return function (other) { return value === other; }; };
    /** Given a value, returns a function which returns the value */
    var val = function (v) { return function () { return v; }; };
    function invoke(fnName, args) {
        return function (obj) { return obj[fnName].apply(obj, args); };
    }
    /**
     * Sorta like Pattern Matching (a functional programming conditional construct)
     *
     * See http://c2.com/cgi/wiki?PatternMatching
     *
     * This is a conditional construct which allows a series of predicates and output functions
     * to be checked and then applied.  Each predicate receives the input.  If the predicate
     * returns truthy, then its matching output function (mapping function) is provided with
     * the input and, then the result is returned.
     *
     * Each combination (2-tuple) of predicate + output function should be placed in an array
     * of size 2: [ predicate, mapFn ]
     *
     * These 2-tuples should be put in an outer array.
     *
     * @example
     * ```
     *
     * // Here's a 2-tuple where the first element is the isString predicate
     * // and the second element is a function that returns a description of the input
     * let firstTuple = [ angular.isString, (input) => `Heres your string ${input}` ];
     *
     * // Second tuple: predicate "isNumber", mapfn returns a description
     * let secondTuple = [ angular.isNumber, (input) => `(${input}) That's a number!` ];
     *
     * let third = [ (input) => input === null,  (input) => `Oh, null...` ];
     *
     * let fourth = [ (input) => input === undefined,  (input) => `notdefined` ];
     *
     * let descriptionOf = pattern([ firstTuple, secondTuple, third, fourth ]);
     *
     * console.log(descriptionOf(undefined)); // 'notdefined'
     * console.log(descriptionOf(55)); // '(55) That's a number!'
     * console.log(descriptionOf("foo")); // 'Here's your string foo'
     * ```
     *
     * @param struct A 2D array.  Each element of the array should be an array, a 2-tuple,
     * with a Predicate and a mapping/output function
     * @returns {function(any): *}
     */
    function pattern(struct) {
        return function (x) {
            for (var i = 0; i < struct.length; i++) {
                if (struct[i][0](x))
                    return struct[i][1](x);
            }
        };
    }

    /**
     * Predicates
     *
     * These predicates return true/false based on the input.
     * Although these functions are exported, they are subject to change without notice.
     *
     * @packageDocumentation
     */
    var toStr = Object.prototype.toString;
    var tis = function (t) { return function (x) { return typeof x === t; }; };
    var isUndefined = tis('undefined');
    var isDefined = not(isUndefined);
    var isNull = function (o) { return o === null; };
    var isNullOrUndefined = or(isNull, isUndefined);
    var isFunction = tis('function');
    var isNumber = tis('number');
    var isString = tis('string');
    var isObject = function (x) { return x !== null && typeof x === 'object'; };
    var isArray = Array.isArray;
    var isDate = (function (x) { return toStr.call(x) === '[object Date]'; });
    var isRegExp = (function (x) { return toStr.call(x) === '[object RegExp]'; });
    /**
     * Predicate which checks if a value is injectable
     *
     * A value is "injectable" if it is a function, or if it is an ng1 array-notation-style array
     * where all the elements in the array are Strings, except the last one, which is a Function
     */
    function isInjectable(val) {
        if (isArray(val) && val.length) {
            var head = val.slice(0, -1), tail = val.slice(-1);
            return !(head.filter(not(isString)).length || tail.filter(not(isFunction)).length);
        }
        return isFunction(val);
    }
    /**
     * Predicate which checks if a value looks like a Promise
     *
     * It is probably a Promise if it's an object, and it has a `then` property which is a Function
     */
    var isPromise = and(isObject, pipe(prop('then'), isFunction));

    var noImpl = function (fnname) { return function () {
        throw new Error("No implementation for " + fnname + ". The framework specific code did not implement this method.");
    }; };
    var makeStub = function (service, methods) {
        return methods.reduce(function (acc, key) { return ((acc[key] = noImpl(service + "." + key + "()")), acc); }, {});
    };
    var services = {
        $q: undefined,
        $injector: undefined,
    };

    var __spreadArrays$1 = (undefined && undefined.__spreadArrays) || function () {
        for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
        for (var r = Array(s), k = 0, i = 0; i < il; i++)
            for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
                r[k] = a[j];
        return r;
    };
    var root = (typeof self === 'object' && self.self === self && self) ||
        (typeof global === 'object' && global.global === global && global) ||
        undefined;
    var angular$1 = root.angular || {};
    var fromJson = angular$1.fromJson || JSON.parse.bind(JSON);
    var toJson = angular$1.toJson || JSON.stringify.bind(JSON);
    var forEach = angular$1.forEach || _forEach;
    var extend = Object.assign || _extend;
    var equals = angular$1.equals || _equals;
    function identity(x) {
        return x;
    }
    function noop() { }
    /**
     * Builds proxy functions on the `to` object which pass through to the `from` object.
     *
     * For each key in `fnNames`, creates a proxy function on the `to` object.
     * The proxy function calls the real function on the `from` object.
     *
     *
     * #### Example:
     * This example creates an new class instance whose functions are prebound to the new'd object.
     * ```js
     * class Foo {
     *   constructor(data) {
     *     // Binds all functions from Foo.prototype to 'this',
     *     // then copies them to 'this'
     *     bindFunctions(Foo.prototype, this, this);
     *     this.data = data;
     *   }
     *
     *   log() {
     *     console.log(this.data);
     *   }
     * }
     *
     * let myFoo = new Foo([1,2,3]);
     * var logit = myFoo.log;
     * logit(); // logs [1, 2, 3] from the myFoo 'this' instance
     * ```
     *
     * #### Example:
     * This example creates a bound version of a service function, and copies it to another object
     * ```
     *
     * var SomeService = {
     *   this.data = [3, 4, 5];
     *   this.log = function() {
     *     console.log(this.data);
     *   }
     * }
     *
     * // Constructor fn
     * function OtherThing() {
     *   // Binds all functions from SomeService to SomeService,
     *   // then copies them to 'this'
     *   bindFunctions(SomeService, this, SomeService);
     * }
     *
     * let myOtherThing = new OtherThing();
     * myOtherThing.log(); // logs [3, 4, 5] from SomeService's 'this'
     * ```
     *
     * @param source A function that returns the source object which contains the original functions to be bound
     * @param target A function that returns the target object which will receive the bound functions
     * @param bind A function that returns the object which the functions will be bound to
     * @param fnNames The function names which will be bound (Defaults to all the functions found on the 'from' object)
     * @param latebind If true, the binding of the function is delayed until the first time it's invoked
     */
    function createProxyFunctions(source, target, bind, fnNames, latebind) {
        if (latebind === void 0) { latebind = false; }
        var bindFunction = function (fnName) { return source()[fnName].bind(bind()); };
        var makeLateRebindFn = function (fnName) {
            return function lateRebindFunction() {
                target[fnName] = bindFunction(fnName);
                return target[fnName].apply(null, arguments);
            };
        };
        fnNames = fnNames || Object.keys(source());
        return fnNames.reduce(function (acc, name) {
            acc[name] = latebind ? makeLateRebindFn(name) : bindFunction(name);
            return acc;
        }, target);
    }
    /**
     * prototypal inheritance helper.
     * Creates a new object which has `parent` object as its prototype, and then copies the properties from `extra` onto it
     */
    var inherit = function (parent, extra) { return extend(Object.create(parent), extra); };
    /** Given an array, returns true if the object is found in the array, (using indexOf) */
    var inArray = curry(_inArray);
    function _inArray(array, obj) {
        return array.indexOf(obj) !== -1;
    }
    /**
     * Given an array, and an item, if the item is found in the array, it removes it (in-place).
     * The same array is returned
     */
    var removeFrom = curry(_removeFrom);
    function _removeFrom(array, obj) {
        var idx = array.indexOf(obj);
        if (idx >= 0)
            array.splice(idx, 1);
        return array;
    }
    /** pushes a values to an array and returns the value */
    var pushTo = curry(_pushTo);
    function _pushTo(arr, val) {
        return arr.push(val), val;
    }
    /** Given an array of (deregistration) functions, calls all functions and removes each one from the source array */
    var deregAll = function (functions) {
        return functions.slice().forEach(function (fn) {
            typeof fn === 'function' && fn();
            removeFrom(functions, fn);
        });
    };
    /**
     * Applies a set of defaults to an options object.  The options object is filtered
     * to only those properties of the objects in the defaultsList.
     * Earlier objects in the defaultsList take precedence when applying defaults.
     */
    function defaults(opts) {
        var defaultsList = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            defaultsList[_i - 1] = arguments[_i];
        }
        var defaultVals = extend.apply(void 0, __spreadArrays$1([{}], defaultsList.reverse()));
        return extend(defaultVals, pick(opts || {}, Object.keys(defaultVals)));
    }
    /** Reduce function that merges each element of the list into a single object, using extend */
    var mergeR = function (memo, item) { return extend(memo, item); };
    /**
     * Finds the common ancestor path between two states.
     *
     * @param {Object} first The first state.
     * @param {Object} second The second state.
     * @return {Array} Returns an array of state names in descending order, not including the root.
     */
    function ancestors(first, second) {
        var path = [];
        // tslint:disable-next-line:forin
        for (var n in first.path) {
            if (first.path[n] !== second.path[n])
                break;
            path.push(first.path[n]);
        }
        return path;
    }
    /**
     * Return a copy of the object only containing the whitelisted properties.
     *
     * #### Example:
     * ```
     * var foo = { a: 1, b: 2, c: 3 };
     * var ab = pick(foo, ['a', 'b']); // { a: 1, b: 2 }
     * ```
     * @param obj the source object
     * @param propNames an Array of strings, which are the whitelisted property names
     */
    function pick(obj, propNames) {
        var objCopy = {};
        for (var _prop in obj) {
            if (propNames.indexOf(_prop) !== -1) {
                objCopy[_prop] = obj[_prop];
            }
        }
        return objCopy;
    }
    /**
     * Return a copy of the object omitting the blacklisted properties.
     *
     * @example
     * ```
     *
     * var foo = { a: 1, b: 2, c: 3 };
     * var ab = omit(foo, ['a', 'b']); // { c: 3 }
     * ```
     * @param obj the source object
     * @param propNames an Array of strings, which are the blacklisted property names
     */
    function omit(obj, propNames) {
        return Object.keys(obj)
            .filter(not(inArray(propNames)))
            .reduce(function (acc, key) { return ((acc[key] = obj[key]), acc); }, {});
    }
    /**
     * Maps an array, or object to a property (by name)
     */
    function pluck(collection, propName) {
        return map(collection, prop(propName));
    }
    /** Filters an Array or an Object's properties based on a predicate */
    function filter(collection, callback) {
        var arr = isArray(collection), result = arr ? [] : {};
        var accept = arr ? function (x) { return result.push(x); } : function (x, key) { return (result[key] = x); };
        forEach(collection, function (item, i) {
            if (callback(item, i))
                accept(item, i);
        });
        return result;
    }
    /** Finds an object from an array, or a property of an object, that matches a predicate */
    function find(collection, callback) {
        var result;
        forEach(collection, function (item, i) {
            if (result)
                return;
            if (callback(item, i))
                result = item;
        });
        return result;
    }
    /** Given an object, returns a new object, where each property is transformed by the callback function */
    var mapObj = map;
    /** Maps an array or object properties using a callback function */
    function map(collection, callback, target) {
        target = target || (isArray(collection) ? [] : {});
        forEach(collection, function (item, i) { return (target[i] = callback(item, i)); });
        return target;
    }
    /**
     * Given an object, return its enumerable property values
     *
     * @example
     * ```
     *
     * let foo = { a: 1, b: 2, c: 3 }
     * let vals = values(foo); // [ 1, 2, 3 ]
     * ```
     */
    var values = function (obj) { return Object.keys(obj).map(function (key) { return obj[key]; }); };
    /**
     * Reduce function that returns true if all of the values are truthy.
     *
     * @example
     * ```
     *
     * let vals = [ 1, true, {}, "hello world"];
     * vals.reduce(allTrueR, true); // true
     *
     * vals.push(0);
     * vals.reduce(allTrueR, true); // false
     * ```
     */
    var allTrueR = function (memo, elem) { return memo && elem; };
    /**
     * Reduce function that returns true if any of the values are truthy.
     *
     *  * @example
     * ```
     *
     * let vals = [ 0, null, undefined ];
     * vals.reduce(anyTrueR, true); // false
     *
     * vals.push("hello world");
     * vals.reduce(anyTrueR, true); // true
     * ```
     */
    var anyTrueR = function (memo, elem) { return memo || elem; };
    /**
     * Reduce function which un-nests a single level of arrays
     * @example
     * ```
     *
     * let input = [ [ "a", "b" ], [ "c", "d" ], [ [ "double", "nested" ] ] ];
     * input.reduce(unnestR, []) // [ "a", "b", "c", "d", [ "double, "nested" ] ]
     * ```
     */
    var unnestR = function (memo, elem) { return memo.concat(elem); };
    /**
     * Reduce function which recursively un-nests all arrays
     *
     * @example
     * ```
     *
     * let input = [ [ "a", "b" ], [ "c", "d" ], [ [ "double", "nested" ] ] ];
     * input.reduce(unnestR, []) // [ "a", "b", "c", "d", "double, "nested" ]
     * ```
     */
    var flattenR = function (memo, elem) {
        return isArray(elem) ? memo.concat(elem.reduce(flattenR, [])) : pushR(memo, elem);
    };
    /**
     * Reduce function that pushes an object to an array, then returns the array.
     * Mostly just for [[flattenR]] and [[uniqR]]
     */
    function pushR(arr, obj) {
        arr.push(obj);
        return arr;
    }
    /** Reduce function that filters out duplicates */
    var uniqR = function (acc, token) { return (inArray(acc, token) ? acc : pushR(acc, token)); };
    /**
     * Return a new array with a single level of arrays unnested.
     *
     * @example
     * ```
     *
     * let input = [ [ "a", "b" ], [ "c", "d" ], [ [ "double", "nested" ] ] ];
     * unnest(input) // [ "a", "b", "c", "d", [ "double, "nested" ] ]
     * ```
     */
    var unnest = function (arr) { return arr.reduce(unnestR, []); };
    /**
     * Return a completely flattened version of an array.
     *
     * @example
     * ```
     *
     * let input = [ [ "a", "b" ], [ "c", "d" ], [ [ "double", "nested" ] ] ];
     * flatten(input) // [ "a", "b", "c", "d", "double, "nested" ]
     * ```
     */
    var flatten = function (arr) { return arr.reduce(flattenR, []); };
    /**
     * Given a .filter Predicate, builds a .filter Predicate which throws an error if any elements do not pass.
     * @example
     * ```
     *
     * let isNumber = (obj) => typeof(obj) === 'number';
     * let allNumbers = [ 1, 2, 3, 4, 5 ];
     * allNumbers.filter(assertPredicate(isNumber)); //OK
     *
     * let oneString = [ 1, 2, 3, 4, "5" ];
     * oneString.filter(assertPredicate(isNumber, "Not all numbers")); // throws Error(""Not all numbers"");
     * ```
     */
    var assertPredicate = assertFn;
    /**
     * Given a .map function, builds a .map function which throws an error if any mapped elements do not pass a truthyness test.
     * @example
     * ```
     *
     * var data = { foo: 1, bar: 2 };
     *
     * let keys = [ 'foo', 'bar' ]
     * let values = keys.map(assertMap(key => data[key], "Key not found"));
     * // values is [1, 2]
     *
     * let keys = [ 'foo', 'bar', 'baz' ]
     * let values = keys.map(assertMap(key => data[key], "Key not found"));
     * // throws Error("Key not found")
     * ```
     */
    var assertMap = assertFn;
    function assertFn(predicateOrMap, errMsg) {
        if (errMsg === void 0) { errMsg = 'assert failure'; }
        return function (obj) {
            var result = predicateOrMap(obj);
            if (!result) {
                throw new Error(isFunction(errMsg) ? errMsg(obj) : errMsg);
            }
            return result;
        };
    }
    /**
     * Like _.pairs: Given an object, returns an array of key/value pairs
     *
     * @example
     * ```
     *
     * pairs({ foo: "FOO", bar: "BAR }) // [ [ "foo", "FOO" ], [ "bar": "BAR" ] ]
     * ```
     */
    var pairs = function (obj) { return Object.keys(obj).map(function (key) { return [key, obj[key]]; }); };
    /**
     * Given two or more parallel arrays, returns an array of tuples where
     * each tuple is composed of [ a[i], b[i], ... z[i] ]
     *
     * @example
     * ```
     *
     * let foo = [ 0, 2, 4, 6 ];
     * let bar = [ 1, 3, 5, 7 ];
     * let baz = [ 10, 30, 50, 70 ];
     * arrayTuples(foo, bar);       // [ [0, 1], [2, 3], [4, 5], [6, 7] ]
     * arrayTuples(foo, bar, baz);  // [ [0, 1, 10], [2, 3, 30], [4, 5, 50], [6, 7, 70] ]
     * ```
     */
    function arrayTuples() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (args.length === 0)
            return [];
        var maxArrayLen = args.reduce(function (min, arr) { return Math.min(arr.length, min); }, 9007199254740991); // aka 2^53 − 1 aka Number.MAX_SAFE_INTEGER
        var result = [];
        var _loop_1 = function (i) {
            // This is a hot function
            // Unroll when there are 1-4 arguments
            switch (args.length) {
                case 1:
                    result.push([args[0][i]]);
                    break;
                case 2:
                    result.push([args[0][i], args[1][i]]);
                    break;
                case 3:
                    result.push([args[0][i], args[1][i], args[2][i]]);
                    break;
                case 4:
                    result.push([args[0][i], args[1][i], args[2][i], args[3][i]]);
                    break;
                default:
                    result.push(args.map(function (array) { return array[i]; }));
                    break;
            }
        };
        for (var i = 0; i < maxArrayLen; i++) {
            _loop_1(i);
        }
        return result;
    }
    /**
     * Reduce function which builds an object from an array of [key, value] pairs.
     *
     * Each iteration sets the key/val pair on the memo object, then returns the memo for the next iteration.
     *
     * Each keyValueTuple should be an array with values [ key: string, value: any ]
     *
     * @example
     * ```
     *
     * var pairs = [ ["fookey", "fooval"], ["barkey", "barval"] ]
     *
     * var pairsToObj = pairs.reduce((memo, pair) => applyPairs(memo, pair), {})
     * // pairsToObj == { fookey: "fooval", barkey: "barval" }
     *
     * // Or, more simply:
     * var pairsToObj = pairs.reduce(applyPairs, {})
     * // pairsToObj == { fookey: "fooval", barkey: "barval" }
     * ```
     */
    function applyPairs(memo, keyValTuple) {
        var key, value;
        if (isArray(keyValTuple))
            key = keyValTuple[0], value = keyValTuple[1];
        if (!isString(key))
            throw new Error('invalid parameters to applyPairs');
        memo[key] = value;
        return memo;
    }
    /** Get the last element of an array */
    function tail(arr) {
        return (arr.length && arr[arr.length - 1]) || undefined;
    }
    /**
     * shallow copy from src to dest
     */
    function copy(src, dest) {
        if (dest)
            Object.keys(dest).forEach(function (key) { return delete dest[key]; });
        if (!dest)
            dest = {};
        return extend(dest, src);
    }
    /** Naive forEach implementation works with Objects or Arrays */
    function _forEach(obj, cb, _this) {
        if (isArray(obj))
            return obj.forEach(cb, _this);
        Object.keys(obj).forEach(function (key) { return cb(obj[key], key); });
    }
    function _extend(toObj) {
        for (var i = 1; i < arguments.length; i++) {
            var obj = arguments[i];
            if (!obj)
                continue;
            var keys = Object.keys(obj);
            for (var j = 0; j < keys.length; j++) {
                toObj[keys[j]] = obj[keys[j]];
            }
        }
        return toObj;
    }
    function _equals(o1, o2) {
        if (o1 === o2)
            return true;
        if (o1 === null || o2 === null)
            return false;
        if (o1 !== o1 && o2 !== o2)
            return true; // NaN === NaN
        var t1 = typeof o1, t2 = typeof o2;
        if (t1 !== t2 || t1 !== 'object')
            return false;
        var tup = [o1, o2];
        if (all(isArray)(tup))
            return _arraysEq(o1, o2);
        if (all(isDate)(tup))
            return o1.getTime() === o2.getTime();
        if (all(isRegExp)(tup))
            return o1.toString() === o2.toString();
        if (all(isFunction)(tup))
            return true; // meh
        var predicates = [isFunction, isArray, isDate, isRegExp];
        if (predicates.map(any).reduce(function (b, fn) { return b || !!fn(tup); }, false))
            return false;
        var keys = {};
        // tslint:disable-next-line:forin
        for (var key in o1) {
            if (!_equals(o1[key], o2[key]))
                return false;
            keys[key] = true;
        }
        for (var key in o2) {
            if (!keys[key])
                return false;
        }
        return true;
    }
    function _arraysEq(a1, a2) {
        if (a1.length !== a2.length)
            return false;
        return arrayTuples(a1, a2).reduce(function (b, t) { return b && _equals(t[0], t[1]); }, true);
    }
    // issue #2676
    var silenceUncaughtInPromise = function (promise) { return promise.catch(function (e) { return 0; }) && promise; };
    var silentRejection = function (error) { return silenceUncaughtInPromise(services.$q.reject(error)); };

    /**
     * Matches state names using glob-like pattern strings.
     *
     * Globs can be used in specific APIs including:
     *
     * - [[StateService.is]]
     * - [[StateService.includes]]
     * - The first argument to Hook Registration functions like [[TransitionService.onStart]]
     *    - [[HookMatchCriteria]] and [[HookMatchCriterion]]
     *
     * A `Glob` string is a pattern which matches state names.
     * Nested state names are split into segments (separated by a dot) when processing.
     * The state named `foo.bar.baz` is split into three segments ['foo', 'bar', 'baz']
     *
     * Globs work according to the following rules:
     *
     * ### Exact match:
     *
     * The glob `'A.B'` matches the state named exactly `'A.B'`.
     *
     * | Glob        |Matches states named|Does not match state named|
     * |:------------|:--------------------|:---------------------|
     * | `'A'`       | `'A'`               | `'B'` , `'A.C'`      |
     * | `'A.B'`     | `'A.B'`             | `'A'` , `'A.B.C'`    |
     * | `'foo'`     | `'foo'`             | `'FOO'` , `'foo.bar'`|
     *
     * ### Single star (`*`)
     *
     * A single star (`*`) is a wildcard that matches exactly one segment.
     *
     * | Glob        |Matches states named  |Does not match state named |
     * |:------------|:---------------------|:--------------------------|
     * | `'*'`       | `'A'` , `'Z'`        | `'A.B'` , `'Z.Y.X'`       |
     * | `'A.*'`     | `'A.B'` , `'A.C'`    | `'A'` , `'A.B.C'`         |
     * | `'A.*.*'`   | `'A.B.C'` , `'A.X.Y'`| `'A'`, `'A.B'` , `'Z.Y.X'`|
     *
     * ### Double star (`**`)
     *
     * A double star (`'**'`) is a wildcard that matches *zero or more segments*
     *
     * | Glob        |Matches states named                           |Does not match state named         |
     * |:------------|:----------------------------------------------|:----------------------------------|
     * | `'**'`      | `'A'` , `'A.B'`, `'Z.Y.X'`                    | (matches all states)              |
     * | `'A.**'`    | `'A'` , `'A.B'` , `'A.C.X'`                   | `'Z.Y.X'`                         |
     * | `'**.X'`    | `'X'` , `'A.X'` , `'Z.Y.X'`                   | `'A'` , `'A.login.Z'`             |
     * | `'A.**.X'`  | `'A.X'` , `'A.B.X'` , `'A.B.C.X'`             | `'A'` , `'A.B.C'`                 |
     *
     * @packageDocumentation
     */
    var Glob = /** @class */ (function () {
        function Glob(text) {
            this.text = text;
            this.glob = text.split('.');
            var regexpString = this.text
                .split('.')
                .map(function (seg) {
                if (seg === '**')
                    return '(?:|(?:\\.[^.]*)*)';
                if (seg === '*')
                    return '\\.[^.]*';
                return '\\.' + seg;
            })
                .join('');
            this.regexp = new RegExp('^' + regexpString + '$');
        }
        /** Returns true if the string has glob-like characters in it */
        Glob.is = function (text) {
            return !!/[!,*]+/.exec(text);
        };
        /** Returns a glob from the string, or null if the string isn't Glob-like */
        Glob.fromString = function (text) {
            return Glob.is(text) ? new Glob(text) : null;
        };
        Glob.prototype.matches = function (name) {
            return this.regexp.test('.' + name);
        };
        return Glob;
    }());

    var Queue = /** @class */ (function () {
        function Queue(_items, _limit) {
            if (_items === void 0) { _items = []; }
            if (_limit === void 0) { _limit = null; }
            this._items = _items;
            this._limit = _limit;
            this._evictListeners = [];
            this.onEvict = pushTo(this._evictListeners);
        }
        Queue.prototype.enqueue = function (item) {
            var items = this._items;
            items.push(item);
            if (this._limit && items.length > this._limit)
                this.evict();
            return item;
        };
        Queue.prototype.evict = function () {
            var item = this._items.shift();
            this._evictListeners.forEach(function (fn) { return fn(item); });
            return item;
        };
        Queue.prototype.dequeue = function () {
            if (this.size())
                return this._items.splice(0, 1)[0];
        };
        Queue.prototype.clear = function () {
            var current = this._items;
            this._items = [];
            return current;
        };
        Queue.prototype.size = function () {
            return this._items.length;
        };
        Queue.prototype.remove = function (item) {
            var idx = this._items.indexOf(item);
            return idx > -1 && this._items.splice(idx, 1)[0];
        };
        Queue.prototype.peekTail = function () {
            return this._items[this._items.length - 1];
        };
        Queue.prototype.peekHead = function () {
            if (this.size())
                return this._items[0];
        };
        return Queue;
    }());

    /** An enum for Transition Rejection reasons */

    (function (RejectType) {
        /**
         * A new transition superseded this one.
         *
         * While this transition was running, a new transition started.
         * This transition is cancelled because it was superseded by new transition.
         */
        RejectType[RejectType["SUPERSEDED"] = 2] = "SUPERSEDED";
        /**
         * The transition was aborted
         *
         * The transition was aborted by a hook which returned `false`
         */
        RejectType[RejectType["ABORTED"] = 3] = "ABORTED";
        /**
         * The transition was invalid
         *
         * The transition was never started because it was invalid
         */
        RejectType[RejectType["INVALID"] = 4] = "INVALID";
        /**
         * The transition was ignored
         *
         * The transition was ignored because it would have no effect.
         *
         * Either:
         *
         * - The transition is targeting the current state and parameter values
         * - The transition is targeting the same state and parameter values as the currently running transition.
         */
        RejectType[RejectType["IGNORED"] = 5] = "IGNORED";
        /**
         * The transition errored.
         *
         * This generally means a hook threw an error or returned a rejected promise
         */
        RejectType[RejectType["ERROR"] = 6] = "ERROR";
    })(exports.RejectType || (exports.RejectType = {}));
    /** @internal */
    var id = 0;
    var Rejection = /** @class */ (function () {
        function Rejection(type, message, detail) {
            /** @internal */
            this.$id = id++;
            this.type = type;
            this.message = message;
            this.detail = detail;
        }
        /** Returns true if the obj is a rejected promise created from the `asPromise` factory */
        Rejection.isRejectionPromise = function (obj) {
            return obj && typeof obj.then === 'function' && is(Rejection)(obj._transitionRejection);
        };
        /** Returns a Rejection due to transition superseded */
        Rejection.superseded = function (detail, options) {
            var message = 'The transition has been superseded by a different transition';
            var rejection = new Rejection(exports.RejectType.SUPERSEDED, message, detail);
            if (options && options.redirected) {
                rejection.redirected = true;
            }
            return rejection;
        };
        /** Returns a Rejection due to redirected transition */
        Rejection.redirected = function (detail) {
            return Rejection.superseded(detail, { redirected: true });
        };
        /** Returns a Rejection due to invalid transition */
        Rejection.invalid = function (detail) {
            var message = 'This transition is invalid';
            return new Rejection(exports.RejectType.INVALID, message, detail);
        };
        /** Returns a Rejection due to ignored transition */
        Rejection.ignored = function (detail) {
            var message = 'The transition was ignored';
            return new Rejection(exports.RejectType.IGNORED, message, detail);
        };
        /** Returns a Rejection due to aborted transition */
        Rejection.aborted = function (detail) {
            var message = 'The transition has been aborted';
            return new Rejection(exports.RejectType.ABORTED, message, detail);
        };
        /** Returns a Rejection due to aborted transition */
        Rejection.errored = function (detail) {
            var message = 'The transition errored';
            return new Rejection(exports.RejectType.ERROR, message, detail);
        };
        /**
         * Returns a Rejection
         *
         * Normalizes a value as a Rejection.
         * If the value is already a Rejection, returns it.
         * Otherwise, wraps and returns the value as a Rejection (Rejection type: ERROR).
         *
         * @returns `detail` if it is already a `Rejection`, else returns an ERROR Rejection.
         */
        Rejection.normalize = function (detail) {
            return is(Rejection)(detail) ? detail : Rejection.errored(detail);
        };
        Rejection.prototype.toString = function () {
            var detailString = function (d) { return (d && d.toString !== Object.prototype.toString ? d.toString() : stringify(d)); };
            var detail = detailString(this.detail);
            var _a = this, $id = _a.$id, type = _a.type, message = _a.message;
            return "Transition Rejection($id: " + $id + " type: " + type + ", message: " + message + ", detail: " + detail + ")";
        };
        Rejection.prototype.toPromise = function () {
            return extend(silentRejection(this), { _transitionRejection: this });
        };
        return Rejection;
    }());

    /**
     * Functions that manipulate strings
     *
     * Although these functions are exported, they are subject to change without notice.
     *
     * @packageDocumentation
     */
    /**
     * Returns a string shortened to a maximum length
     *
     * If the string is already less than the `max` length, return the string.
     * Else return the string, shortened to `max - 3` and append three dots ("...").
     *
     * @param max the maximum length of the string to return
     * @param str the input string
     */
    function maxLength(max, str) {
        if (str.length <= max)
            return str;
        return str.substr(0, max - 3) + '...';
    }
    /**
     * Returns a string, with spaces added to the end, up to a desired str length
     *
     * If the string is already longer than the desired length, return the string.
     * Else returns the string, with extra spaces on the end, such that it reaches `length` characters.
     *
     * @param length the desired length of the string to return
     * @param str the input string
     */
    function padString(length, str) {
        while (str.length < length)
            str += ' ';
        return str;
    }
    function kebobString(camelCase) {
        return camelCase
            .replace(/^([A-Z])/, function ($1) { return $1.toLowerCase(); }) // replace first char
            .replace(/([A-Z])/g, function ($1) { return '-' + $1.toLowerCase(); }); // replace rest
    }
    function functionToString(fn) {
        var fnStr = fnToString(fn);
        var namedFunctionMatch = fnStr.match(/^(function [^ ]+\([^)]*\))/);
        var toStr = namedFunctionMatch ? namedFunctionMatch[1] : fnStr;
        var fnName = fn['name'] || '';
        if (fnName && toStr.match(/function \(/)) {
            return 'function ' + fnName + toStr.substr(9);
        }
        return toStr;
    }
    function fnToString(fn) {
        var _fn = isArray(fn) ? fn.slice(-1)[0] : fn;
        return (_fn && _fn.toString()) || 'undefined';
    }
    function stringify(o) {
        var seen = [];
        var isRejection = Rejection.isRejectionPromise;
        var hasToString = function (obj) {
            return isObject(obj) && !isArray(obj) && obj.constructor !== Object && isFunction(obj.toString);
        };
        var stringifyPattern = pattern([
            [isUndefined, val('undefined')],
            [isNull, val('null')],
            [isPromise, val('[Promise]')],
            [isRejection, function (x) { return x._transitionRejection.toString(); }],
            [hasToString, function (x) { return x.toString(); }],
            [isInjectable, functionToString],
            [val(true), identity],
        ]);
        function format(value) {
            if (isObject(value)) {
                if (seen.indexOf(value) !== -1)
                    return '[circular ref]';
                seen.push(value);
            }
            return stringifyPattern(value);
        }
        if (isUndefined(o)) {
            // Workaround for IE & Edge Spec incompatibility where replacer function would not be called when JSON.stringify
            // is given `undefined` as value. To work around that, we simply detect `undefined` and bail out early by
            // manually stringifying it.
            return format(o);
        }
        return JSON.stringify(o, function (key, value) { return format(value); }).replace(/\\"/g, '"');
    }
    /** Returns a function that splits a string on a character or substring */
    var beforeAfterSubstr = function (char) {
        return function (str) {
            if (!str)
                return ['', ''];
            var idx = str.indexOf(char);
            if (idx === -1)
                return [str, ''];
            return [str.substr(0, idx), str.substr(idx + 1)];
        };
    };
    var hostRegex = new RegExp('^(?:[a-z]+:)?//[^/]+/');
    var stripLastPathElement = function (str) { return str.replace(/\/[^/]*$/, ''); };
    var splitHash = beforeAfterSubstr('#');
    var splitQuery = beforeAfterSubstr('?');
    var splitEqual = beforeAfterSubstr('=');
    var trimHashVal = function (str) { return (str ? str.replace(/^#/, '') : ''); };
    /**
     * Splits on a delimiter, but returns the delimiters in the array
     *
     * #### Example:
     * ```js
     * var splitOnSlashes = splitOnDelim('/');
     * splitOnSlashes("/foo"); // ["/", "foo"]
     * splitOnSlashes("/foo/"); // ["/", "foo", "/"]
     * ```
     */
    function splitOnDelim(delim) {
        var re = new RegExp('(' + delim + ')', 'g');
        return function (str) { return str.split(re).filter(identity); };
    }
    /**
     * Reduce fn that joins neighboring strings
     *
     * Given an array of strings, returns a new array
     * where all neighboring strings have been joined.
     *
     * #### Example:
     * ```js
     * let arr = ["foo", "bar", 1, "baz", "", "qux" ];
     * arr.reduce(joinNeighborsR, []) // ["foobar", 1, "bazqux" ]
     * ```
     */
    function joinNeighborsR(acc, x) {
        if (isString(tail(acc)) && isString(x))
            return acc.slice(0, -1).concat(tail(acc) + x);
        return pushR(acc, x);
    }

    /**
     * workaround for missing console object in IE9 when dev tools haven't been opened o_O
     * @packageDocumentation
     */
    var noopConsoleStub = { log: noop, error: noop, table: noop };
    function ie9Console(console) {
        var bound = function (fn) { return Function.prototype.bind.call(fn, console); };
        return {
            log: bound(console.log),
            error: bound(console.log),
            table: bound(console.log),
        };
    }
    function fallbackConsole(console) {
        var log = console.log.bind(console);
        var error = console.error ? console.error.bind(console) : log;
        var table = console.table ? console.table.bind(console) : log;
        return { log: log, error: error, table: table };
    }
    function getSafeConsole() {
        // @ts-ignore
        var isIE9 = typeof document !== 'undefined' && document.documentMode && document.documentMode === 9;
        if (isIE9) {
            return window && window.console ? ie9Console(window.console) : noopConsoleStub;
        }
        else if (!console.table || !console.error) {
            return fallbackConsole(console);
        }
        else {
            return console;
        }
    }
    var safeConsole = getSafeConsole();

    /**
     * # Transition tracing (debug)
     *
     * Enable transition tracing to print transition information to the console,
     * in order to help debug your application.
     * Tracing logs detailed information about each Transition to your console.
     *
     * To enable tracing, import the [[Trace]] singleton and enable one or more categories.
     *
     * ### ES6
     * ```js
     * import {trace} from "@uirouter/core";
     * trace.enable(1, 5); // TRANSITION and VIEWCONFIG
     * ```
     *
     * ### CJS
     * ```js
     * let trace = require("@uirouter/core").trace;
     * trace.enable("TRANSITION", "VIEWCONFIG");
     * ```
     *
     * ### Globals
     * ```js
     * let trace = window["@uirouter/core"].trace;
     * trace.enable(); // Trace everything (very verbose)
     * ```
     *
     * ### Angular 1:
     * ```js
     * app.run($trace => $trace.enable());
     * ```
     *
     * @packageDocumentation
     */
    function uiViewString(uiview) {
        if (!uiview)
            return 'ui-view (defunct)';
        var state = uiview.creationContext ? uiview.creationContext.name || '(root)' : '(none)';
        return "[ui-view#" + uiview.id + " " + uiview.$type + ":" + uiview.fqn + " (" + uiview.name + "@" + state + ")]";
    }
    var viewConfigString = function (viewConfig) {
        var view = viewConfig.viewDecl;
        var state = view.$context.name || '(root)';
        return "[View#" + viewConfig.$id + " from '" + state + "' state]: target ui-view: '" + view.$uiViewName + "@" + view.$uiViewContextAnchor + "'";
    };
    function normalizedCat(input) {
        return isNumber(input) ? exports.Category[input] : exports.Category[exports.Category[input]];
    }
    /**
     * Trace categories Enum
     *
     * Enable or disable a category using [[Trace.enable]] or [[Trace.disable]]
     *
     * `trace.enable(Category.TRANSITION)`
     *
     * These can also be provided using a matching string, or position ordinal
     *
     * `trace.enable("TRANSITION")`
     *
     * `trace.enable(1)`
     */

    (function (Category) {
        Category[Category["RESOLVE"] = 0] = "RESOLVE";
        Category[Category["TRANSITION"] = 1] = "TRANSITION";
        Category[Category["HOOK"] = 2] = "HOOK";
        Category[Category["UIVIEW"] = 3] = "UIVIEW";
        Category[Category["VIEWCONFIG"] = 4] = "VIEWCONFIG";
    })(exports.Category || (exports.Category = {}));
    var _tid = parse('$id');
    var _rid = parse('router.$id');
    var transLbl = function (trans) { return "Transition #" + _tid(trans) + "-" + _rid(trans); };
    /**
     * Prints UI-Router Transition trace information to the console.
     */
    var Trace = /** @class */ (function () {
        /** @internal */
        function Trace() {
            /** @internal */
            this._enabled = {};
            this.approximateDigests = 0;
        }
        /** @internal */
        Trace.prototype._set = function (enabled, categories) {
            var _this = this;
            if (!categories.length) {
                categories = Object.keys(exports.Category)
                    .map(function (k) { return parseInt(k, 10); })
                    .filter(function (k) { return !isNaN(k); })
                    .map(function (key) { return exports.Category[key]; });
            }
            categories.map(normalizedCat).forEach(function (category) { return (_this._enabled[category] = enabled); });
        };
        Trace.prototype.enable = function () {
            var categories = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                categories[_i] = arguments[_i];
            }
            this._set(true, categories);
        };
        Trace.prototype.disable = function () {
            var categories = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                categories[_i] = arguments[_i];
            }
            this._set(false, categories);
        };
        /**
         * Retrieves the enabled stateus of a [[Category]]
         *
         * ```js
         * trace.enabled("VIEWCONFIG"); // true or false
         * ```
         *
         * @returns boolean true if the category is enabled
         */
        Trace.prototype.enabled = function (category) {
            return !!this._enabled[normalizedCat(category)];
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceTransitionStart = function (trans) {
            if (!this.enabled(exports.Category.TRANSITION))
                return;
            safeConsole.log(transLbl(trans) + ": Started  -> " + stringify(trans));
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceTransitionIgnored = function (trans) {
            if (!this.enabled(exports.Category.TRANSITION))
                return;
            safeConsole.log(transLbl(trans) + ": Ignored  <> " + stringify(trans));
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceHookInvocation = function (step, trans, options) {
            if (!this.enabled(exports.Category.HOOK))
                return;
            var event = parse('traceData.hookType')(options) || 'internal', context = parse('traceData.context.state.name')(options) || parse('traceData.context')(options) || 'unknown', name = functionToString(step.registeredHook.callback);
            safeConsole.log(transLbl(trans) + ":   Hook -> " + event + " context: " + context + ", " + maxLength(200, name));
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceHookResult = function (hookResult, trans, transitionOptions) {
            if (!this.enabled(exports.Category.HOOK))
                return;
            safeConsole.log(transLbl(trans) + ":   <- Hook returned: " + maxLength(200, stringify(hookResult)));
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceResolvePath = function (path, when, trans) {
            if (!this.enabled(exports.Category.RESOLVE))
                return;
            safeConsole.log(transLbl(trans) + ":         Resolving " + path + " (" + when + ")");
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceResolvableResolved = function (resolvable, trans) {
            if (!this.enabled(exports.Category.RESOLVE))
                return;
            safeConsole.log(transLbl(trans) + ":               <- Resolved  " + resolvable + " to: " + maxLength(200, stringify(resolvable.data)));
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceError = function (reason, trans) {
            if (!this.enabled(exports.Category.TRANSITION))
                return;
            safeConsole.log(transLbl(trans) + ": <- Rejected " + stringify(trans) + ", reason: " + reason);
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceSuccess = function (finalState, trans) {
            if (!this.enabled(exports.Category.TRANSITION))
                return;
            safeConsole.log(transLbl(trans) + ": <- Success  " + stringify(trans) + ", final state: " + finalState.name);
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceUIViewEvent = function (event, viewData, extra) {
            if (extra === void 0) { extra = ''; }
            if (!this.enabled(exports.Category.UIVIEW))
                return;
            safeConsole.log("ui-view: " + padString(30, event) + " " + uiViewString(viewData) + extra);
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceUIViewConfigUpdated = function (viewData, context) {
            if (!this.enabled(exports.Category.UIVIEW))
                return;
            this.traceUIViewEvent('Updating', viewData, " with ViewConfig from context='" + context + "'");
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceUIViewFill = function (viewData, html) {
            if (!this.enabled(exports.Category.UIVIEW))
                return;
            this.traceUIViewEvent('Fill', viewData, " with: " + maxLength(200, html));
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceViewSync = function (pairs) {
            if (!this.enabled(exports.Category.VIEWCONFIG))
                return;
            var uivheader = 'uiview component fqn';
            var cfgheader = 'view config state (view name)';
            var mapping = pairs
                .map(function (_a) {
                var _b;
                var uiView = _a.uiView, viewConfig = _a.viewConfig;
                var uiv = uiView && uiView.fqn;
                var cfg = viewConfig && viewConfig.viewDecl.$context.name + ": (" + viewConfig.viewDecl.$name + ")";
                return _b = {}, _b[uivheader] = uiv, _b[cfgheader] = cfg, _b;
            })
                .sort(function (a, b) { return (a[uivheader] || '').localeCompare(b[uivheader] || ''); });
            safeConsole.table(mapping);
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceViewServiceEvent = function (event, viewConfig) {
            if (!this.enabled(exports.Category.VIEWCONFIG))
                return;
            safeConsole.log("VIEWCONFIG: " + event + " " + viewConfigString(viewConfig));
        };
        /** @internal called by ui-router code */
        Trace.prototype.traceViewServiceUIViewEvent = function (event, viewData) {
            if (!this.enabled(exports.Category.VIEWCONFIG))
                return;
            safeConsole.log("VIEWCONFIG: " + event + " " + uiViewString(viewData));
        };
        return Trace;
    }());
    /**
     * The [[Trace]] singleton
     *
     * #### Example:
     * ```js
     * import {trace} from "@uirouter/core";
     * trace.enable(1, 5);
     * ```
     */
    var trace = new Trace();

    /**
     * An internal class which implements [[ParamTypeDefinition]].
     *
     * A [[ParamTypeDefinition]] is a plain javascript object used to register custom parameter types.
     * When a param type definition is registered, an instance of this class is created internally.
     *
     * This class has naive implementations for all the [[ParamTypeDefinition]] methods.
     *
     * Used by [[UrlMatcher]] when matching or formatting URLs, or comparing and validating parameter values.
     *
     * #### Example:
     * ```js
     * var paramTypeDef = {
     *   decode: function(val) { return parseInt(val, 10); },
     *   encode: function(val) { return val && val.toString(); },
     *   equals: function(a, b) { return this.is(a) && a === b; },
     *   is: function(val) { return angular.isNumber(val) && isFinite(val) && val % 1 === 0; },
     *   pattern: /\d+/
     * }
     *
     * var paramType = new ParamType(paramTypeDef);
     * ```
     */
    var ParamType = /** @class */ (function () {
        /**
         * @param def  A configuration object which contains the custom type definition.  The object's
         *        properties will override the default methods and/or pattern in `ParamType`'s public interface.
         * @returns a new ParamType object
         */
        function ParamType(def) {
            /** @inheritdoc */
            this.pattern = /.*/;
            /** @inheritdoc */
            this.inherit = true;
            extend(this, def);
        }
        // consider these four methods to be "abstract methods" that should be overridden
        /** @inheritdoc */
        ParamType.prototype.is = function (val, key) {
            return true;
        };
        /** @inheritdoc */
        ParamType.prototype.encode = function (val, key) {
            return val;
        };
        /** @inheritdoc */
        ParamType.prototype.decode = function (val, key) {
            return val;
        };
        /** @inheritdoc */
        ParamType.prototype.equals = function (a, b) {
            // tslint:disable-next-line:triple-equals
            return a == b;
        };
        ParamType.prototype.$subPattern = function () {
            var sub = this.pattern.toString();
            return sub.substr(1, sub.length - 2);
        };
        ParamType.prototype.toString = function () {
            return "{ParamType:" + this.name + "}";
        };
        /** Given an encoded string, or a decoded object, returns a decoded object */
        ParamType.prototype.$normalize = function (val) {
            return this.is(val) ? val : this.decode(val);
        };
        /**
         * Wraps an existing custom ParamType as an array of ParamType, depending on 'mode'.
         * e.g.:
         * - urlmatcher pattern "/path?{queryParam[]:int}"
         * - url: "/path?queryParam=1&queryParam=2
         * - $stateParams.queryParam will be [1, 2]
         * if `mode` is "auto", then
         * - url: "/path?queryParam=1 will create $stateParams.queryParam: 1
         * - url: "/path?queryParam=1&queryParam=2 will create $stateParams.queryParam: [1, 2]
         */
        ParamType.prototype.$asArray = function (mode, isSearch) {
            if (!mode)
                return this;
            if (mode === 'auto' && !isSearch)
                throw new Error("'auto' array mode is for query parameters only");
            return new ArrayType(this, mode);
        };
        return ParamType;
    }());
    /** Wraps up a `ParamType` object to handle array values. */
    function ArrayType(type, mode) {
        var _this = this;
        // Wrap non-array value as array
        function arrayWrap(val) {
            return isArray(val) ? val : isDefined(val) ? [val] : [];
        }
        // Unwrap array value for "auto" mode. Return undefined for empty array.
        function arrayUnwrap(val) {
            switch (val.length) {
                case 0:
                    return undefined;
                case 1:
                    return mode === 'auto' ? val[0] : val;
                default:
                    return val;
            }
        }
        // Wraps type (.is/.encode/.decode) functions to operate on each value of an array
        function arrayHandler(callback, allTruthyMode) {
            return function handleArray(val) {
                if (isArray(val) && val.length === 0)
                    return val;
                var arr = arrayWrap(val);
                var result = map(arr, callback);
                return allTruthyMode === true ? filter(result, function (x) { return !x; }).length === 0 : arrayUnwrap(result);
            };
        }
        // Wraps type (.equals) functions to operate on each value of an array
        function arrayEqualsHandler(callback) {
            return function handleArray(val1, val2) {
                var left = arrayWrap(val1), right = arrayWrap(val2);
                if (left.length !== right.length)
                    return false;
                for (var i = 0; i < left.length; i++) {
                    if (!callback(left[i], right[i]))
                        return false;
                }
                return true;
            };
        }
        ['encode', 'decode', 'equals', '$normalize'].forEach(function (name) {
            var paramTypeFn = type[name].bind(type);
            var wrapperFn = name === 'equals' ? arrayEqualsHandler : arrayHandler;
            _this[name] = wrapperFn(paramTypeFn);
        });
        extend(this, {
            dynamic: type.dynamic,
            name: type.name,
            pattern: type.pattern,
            inherit: type.inherit,
            raw: type.raw,
            is: arrayHandler(type.is.bind(type), true),
            $arrayMode: mode,
        });
    }

    var hasOwn = Object.prototype.hasOwnProperty;
    var isShorthand = function (cfg) {
        return ['value', 'type', 'squash', 'array', 'dynamic'].filter(hasOwn.bind(cfg || {})).length === 0;
    };

    (function (DefType) {
        DefType[DefType["PATH"] = 0] = "PATH";
        DefType[DefType["SEARCH"] = 1] = "SEARCH";
        DefType[DefType["CONFIG"] = 2] = "CONFIG";
    })(exports.DefType || (exports.DefType = {}));
    function getParamDeclaration(paramName, location, state) {
        var noReloadOnSearch = (state.reloadOnSearch === false && location === exports.DefType.SEARCH) || undefined;
        var dynamic = find([state.dynamic, noReloadOnSearch], isDefined);
        var defaultConfig = isDefined(dynamic) ? { dynamic: dynamic } : {};
        var paramConfig = unwrapShorthand(state && state.params && state.params[paramName]);
        return extend(defaultConfig, paramConfig);
    }
    function unwrapShorthand(cfg) {
        cfg = isShorthand(cfg) ? { value: cfg } : cfg;
        getStaticDefaultValue['__cacheable'] = true;
        function getStaticDefaultValue() {
            return cfg.value;
        }
        var $$fn = isInjectable(cfg.value) ? cfg.value : getStaticDefaultValue;
        return extend(cfg, { $$fn: $$fn });
    }
    function getType(cfg, urlType, location, id, paramTypes) {
        if (cfg.type && urlType && urlType.name !== 'string')
            throw new Error("Param '" + id + "' has two type configurations.");
        if (cfg.type && urlType && urlType.name === 'string' && paramTypes.type(cfg.type))
            return paramTypes.type(cfg.type);
        if (urlType)
            return urlType;
        if (!cfg.type) {
            var type = location === exports.DefType.CONFIG
                ? 'any'
                : location === exports.DefType.PATH
                    ? 'path'
                    : location === exports.DefType.SEARCH
                        ? 'query'
                        : 'string';
            return paramTypes.type(type);
        }
        return cfg.type instanceof ParamType ? cfg.type : paramTypes.type(cfg.type);
    }
    /** returns false, true, or the squash value to indicate the "default parameter url squash policy". */
    function getSquashPolicy(config, isOptional, defaultPolicy) {
        var squash = config.squash;
        if (!isOptional || squash === false)
            return false;
        if (!isDefined(squash) || squash == null)
            return defaultPolicy;
        if (squash === true || isString(squash))
            return squash;
        throw new Error("Invalid squash policy: '" + squash + "'. Valid policies: false, true, or arbitrary string");
    }
    function getReplace(config, arrayMode, isOptional, squash) {
        var defaultPolicy = [
            { from: '', to: isOptional || arrayMode ? undefined : '' },
            { from: null, to: isOptional || arrayMode ? undefined : '' },
        ];
        var replace = isArray(config.replace) ? config.replace : [];
        if (isString(squash))
            replace.push({ from: squash, to: undefined });
        var configuredKeys = map(replace, prop('from'));
        return filter(defaultPolicy, function (item) { return configuredKeys.indexOf(item.from) === -1; }).concat(replace);
    }
    var Param = /** @class */ (function () {
        function Param(id, type, location, urlConfig, state) {
            var config = getParamDeclaration(id, location, state);
            type = getType(config, type, location, id, urlConfig.paramTypes);
            var arrayMode = getArrayMode();
            type = arrayMode ? type.$asArray(arrayMode, location === exports.DefType.SEARCH) : type;
            var isOptional = config.value !== undefined || location === exports.DefType.SEARCH;
            var dynamic = isDefined(config.dynamic) ? !!config.dynamic : !!type.dynamic;
            var raw = isDefined(config.raw) ? !!config.raw : !!type.raw;
            var squash = getSquashPolicy(config, isOptional, urlConfig.defaultSquashPolicy());
            var replace = getReplace(config, arrayMode, isOptional, squash);
            var inherit = isDefined(config.inherit) ? !!config.inherit : !!type.inherit;
            // array config: param name (param[]) overrides default settings.  explicit config overrides param name.
            function getArrayMode() {
                var arrayDefaults = { array: location === exports.DefType.SEARCH ? 'auto' : false };
                var arrayParamNomenclature = id.match(/\[\]$/) ? { array: true } : {};
                return extend(arrayDefaults, arrayParamNomenclature, config).array;
            }
            extend(this, { id: id, type: type, location: location, isOptional: isOptional, dynamic: dynamic, raw: raw, squash: squash, replace: replace, inherit: inherit, array: arrayMode, config: config });
        }
        Param.values = function (params, values) {
            if (values === void 0) { values = {}; }
            var paramValues = {};
            for (var _i = 0, params_1 = params; _i < params_1.length; _i++) {
                var param = params_1[_i];
                paramValues[param.id] = param.value(values[param.id]);
            }
            return paramValues;
        };
        /**
         * Finds [[Param]] objects which have different param values
         *
         * Filters a list of [[Param]] objects to only those whose parameter values differ in two param value objects
         *
         * @param params: The list of Param objects to filter
         * @param values1: The first set of parameter values
         * @param values2: the second set of parameter values
         *
         * @returns any Param objects whose values were different between values1 and values2
         */
        Param.changed = function (params, values1, values2) {
            if (values1 === void 0) { values1 = {}; }
            if (values2 === void 0) { values2 = {}; }
            return params.filter(function (param) { return !param.type.equals(values1[param.id], values2[param.id]); });
        };
        /**
         * Checks if two param value objects are equal (for a set of [[Param]] objects)
         *
         * @param params The list of [[Param]] objects to check
         * @param values1 The first set of param values
         * @param values2 The second set of param values
         *
         * @returns true if the param values in values1 and values2 are equal
         */
        Param.equals = function (params, values1, values2) {
            if (values1 === void 0) { values1 = {}; }
            if (values2 === void 0) { values2 = {}; }
            return Param.changed(params, values1, values2).length === 0;
        };
        /** Returns true if a the parameter values are valid, according to the Param definitions */
        Param.validates = function (params, values) {
            if (values === void 0) { values = {}; }
            return params.map(function (param) { return param.validates(values[param.id]); }).reduce(allTrueR, true);
        };
        Param.prototype.isDefaultValue = function (value) {
            return this.isOptional && this.type.equals(this.value(), value);
        };
        /**
         * [Internal] Gets the decoded representation of a value if the value is defined, otherwise, returns the
         * default value, which may be the result of an injectable function.
         */
        Param.prototype.value = function (value) {
            var _this = this;
            /**
             * [Internal] Get the default value of a parameter, which may be an injectable function.
             */
            var getDefaultValue = function () {
                if (_this._defaultValueCache)
                    return _this._defaultValueCache.defaultValue;
                if (!services.$injector)
                    throw new Error('Injectable functions cannot be called at configuration time');
                var defaultValue = services.$injector.invoke(_this.config.$$fn);
                if (defaultValue !== null && defaultValue !== undefined && !_this.type.is(defaultValue))
                    throw new Error("Default value (" + defaultValue + ") for parameter '" + _this.id + "' is not an instance of ParamType (" + _this.type.name + ")");
                if (_this.config.$$fn['__cacheable']) {
                    _this._defaultValueCache = { defaultValue: defaultValue };
                }
                return defaultValue;
            };
            var replaceSpecialValues = function (val) {
                for (var _i = 0, _a = _this.replace; _i < _a.length; _i++) {
                    var tuple = _a[_i];
                    if (tuple.from === val)
                        return tuple.to;
                }
                return val;
            };
            value = replaceSpecialValues(value);
            return isUndefined(value) ? getDefaultValue() : this.type.$normalize(value);
        };
        Param.prototype.isSearch = function () {
            return this.location === exports.DefType.SEARCH;
        };
        Param.prototype.validates = function (value) {
            // There was no parameter value, but the param is optional
            if ((isUndefined(value) || value === null) && this.isOptional)
                return true;
            // The value was not of the correct ParamType, and could not be decoded to the correct ParamType
            var normalized = this.type.$normalize(value);
            if (!this.type.is(normalized))
                return false;
            // The value was of the correct type, but when encoded, did not match the ParamType's regexp
            var encoded = this.type.encode(normalized);
            return !(isString(encoded) && !this.type.pattern.exec(encoded));
        };
        Param.prototype.toString = function () {
            return "{Param:" + this.id + " " + this.type + " squash: '" + this.squash + "' optional: " + this.isOptional + "}";
        };
        return Param;
    }());

    /**
     * A registry for parameter types.
     *
     * This registry manages the built-in (and custom) parameter types.
     *
     * The built-in parameter types are:
     *
     * - [[string]]
     * - [[path]]
     * - [[query]]
     * - [[hash]]
     * - [[int]]
     * - [[bool]]
     * - [[date]]
     * - [[json]]
     * - [[any]]
     *
     * To register custom parameter types, use [[UrlConfig.type]], i.e.,
     *
     * ```js
     * router.urlService.config.type(customType)
     * ```
     */
    var ParamTypes = /** @class */ (function () {
        function ParamTypes() {
            this.enqueue = true;
            this.typeQueue = [];
            this.defaultTypes = pick(ParamTypes.prototype, [
                'hash',
                'string',
                'query',
                'path',
                'int',
                'bool',
                'date',
                'json',
                'any',
            ]);
            // Register default types. Store them in the prototype of this.types.
            var makeType = function (definition, name) { return new ParamType(extend({ name: name }, definition)); };
            this.types = inherit(map(this.defaultTypes, makeType), {});
        }
        ParamTypes.prototype.dispose = function () {
            this.types = {};
        };
        /**
         * Registers a parameter type
         *
         * End users should call [[UrlMatcherFactory.type]], which delegates to this method.
         */
        ParamTypes.prototype.type = function (name, definition, definitionFn) {
            if (!isDefined(definition))
                return this.types[name];
            if (this.types.hasOwnProperty(name))
                throw new Error("A type named '" + name + "' has already been defined.");
            this.types[name] = new ParamType(extend({ name: name }, definition));
            if (definitionFn) {
                this.typeQueue.push({ name: name, def: definitionFn });
                if (!this.enqueue)
                    this._flushTypeQueue();
            }
            return this;
        };
        ParamTypes.prototype._flushTypeQueue = function () {
            while (this.typeQueue.length) {
                var type = this.typeQueue.shift();
                if (type.pattern)
                    throw new Error("You cannot override a type's .pattern at runtime.");
                extend(this.types[type.name], services.$injector.invoke(type.def));
            }
        };
        return ParamTypes;
    }());
    function initDefaultTypes() {
        var makeDefaultType = function (def) {
            var valToString = function (val) { return (val != null ? val.toString() : val); };
            var defaultTypeBase = {
                encode: valToString,
                decode: valToString,
                is: is(String),
                pattern: /.*/,
                // tslint:disable-next-line:triple-equals
                equals: function (a, b) { return a == b; },
            };
            return extend({}, defaultTypeBase, def);
        };
        // Default Parameter Type Definitions
        extend(ParamTypes.prototype, {
            string: makeDefaultType({}),
            path: makeDefaultType({
                pattern: /[^/]*/,
            }),
            query: makeDefaultType({}),
            hash: makeDefaultType({
                inherit: false,
            }),
            int: makeDefaultType({
                decode: function (val) { return parseInt(val, 10); },
                is: function (val) {
                    return !isNullOrUndefined(val) && this.decode(val.toString()) === val;
                },
                pattern: /-?\d+/,
            }),
            bool: makeDefaultType({
                encode: function (val) { return (val && 1) || 0; },
                decode: function (val) { return parseInt(val, 10) !== 0; },
                is: is(Boolean),
                pattern: /0|1/,
            }),
            date: makeDefaultType({
                encode: function (val) {
                    return !this.is(val)
                        ? undefined
                        : [val.getFullYear(), ('0' + (val.getMonth() + 1)).slice(-2), ('0' + val.getDate()).slice(-2)].join('-');
                },
                decode: function (val) {
                    if (this.is(val))
                        return val;
                    var match = this.capture.exec(val);
                    return match ? new Date(match[1], match[2] - 1, match[3]) : undefined;
                },
                is: function (val) { return val instanceof Date && !isNaN(val.valueOf()); },
                equals: function (l, r) {
                    return ['getFullYear', 'getMonth', 'getDate'].reduce(function (acc, fn) { return acc && l[fn]() === r[fn](); }, true);
                },
                pattern: /[0-9]{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[1-2][0-9]|3[0-1])/,
                capture: /([0-9]{4})-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])/,
            }),
            json: makeDefaultType({
                encode: toJson,
                decode: fromJson,
                is: is(Object),
                equals: equals,
                pattern: /[^/]*/,
            }),
            // does not encode/decode
            any: makeDefaultType({
                encode: identity,
                decode: identity,
                is: function () { return true; },
                equals: equals,
            }),
        });
    }
    initDefaultTypes();

    var StateParams = /** @class */ (function () {
        function StateParams(params) {
            if (params === void 0) { params = {}; }
            extend(this, params);
        }
        /**
         * Merges a set of parameters with all parameters inherited between the common parents of the
         * current state and a given destination state.
         *
         * @param {Object} newParams The set of parameters which will be composited with inherited params.
         * @param {Object} $current Internal definition of object representing the current state.
         * @param {Object} $to Internal definition of object representing state to transition to.
         */
        StateParams.prototype.$inherit = function (newParams, $current, $to) {
            var parentParams;
            var parents = ancestors($current, $to), inherited = {}, inheritList = [];
            for (var i in parents) {
                if (!parents[i] || !parents[i].params)
                    continue;
                parentParams = Object.keys(parents[i].params);
                if (!parentParams.length)
                    continue;
                for (var j in parentParams) {
                    if (inheritList.indexOf(parentParams[j]) >= 0)
                        continue;
                    inheritList.push(parentParams[j]);
                    inherited[parentParams[j]] = this[parentParams[j]];
                }
            }
            return extend({}, inherited, newParams);
        };
        return StateParams;
    }());

    /**
     * A node in a [[TreeChanges]] path
     *
     * For a [[TreeChanges]] path, this class holds the stateful information for a single node in the path.
     * Each PathNode corresponds to a state being entered, exited, or retained.
     * The stateful information includes parameter values and resolve data.
     */
    var PathNode = /** @class */ (function () {
        function PathNode(stateOrNode) {
            if (stateOrNode instanceof PathNode) {
                var node = stateOrNode;
                this.state = node.state;
                this.paramSchema = node.paramSchema.slice();
                this.paramValues = extend({}, node.paramValues);
                this.resolvables = node.resolvables.slice();
                this.views = node.views && node.views.slice();
            }
            else {
                var state = stateOrNode;
                this.state = state;
                this.paramSchema = state.parameters({ inherit: false });
                this.paramValues = {};
                this.resolvables = state.resolvables.map(function (res) { return res.clone(); });
            }
        }
        PathNode.prototype.clone = function () {
            return new PathNode(this);
        };
        /** Sets [[paramValues]] for the node, from the values of an object hash */
        PathNode.prototype.applyRawParams = function (params) {
            var getParamVal = function (paramDef) { return [paramDef.id, paramDef.value(params[paramDef.id])]; };
            this.paramValues = this.paramSchema.reduce(function (memo, pDef) { return applyPairs(memo, getParamVal(pDef)); }, {});
            return this;
        };
        /** Gets a specific [[Param]] metadata that belongs to the node */
        PathNode.prototype.parameter = function (name) {
            return find(this.paramSchema, propEq('id', name));
        };
        /**
         * @returns true if the state and parameter values for another PathNode are
         * equal to the state and param values for this PathNode
         */
        PathNode.prototype.equals = function (node, paramsFn) {
            var diff = this.diff(node, paramsFn);
            return diff && diff.length === 0;
        };
        /**
         * Finds Params with different parameter values on another PathNode.
         *
         * Given another node (of the same state), finds the parameter values which differ.
         * Returns the [[Param]] (schema objects) whose parameter values differ.
         *
         * Given another node for a different state, returns `false`
         *
         * @param node The node to compare to
         * @param paramsFn A function that returns which parameters should be compared.
         * @returns The [[Param]]s which differ, or null if the two nodes are for different states
         */
        PathNode.prototype.diff = function (node, paramsFn) {
            if (this.state !== node.state)
                return false;
            var params = paramsFn ? paramsFn(this) : this.paramSchema;
            return Param.changed(params, this.paramValues, node.paramValues);
        };
        /**
         * Returns a clone of the PathNode
         * @deprecated use instance method `node.clone()`
         */
        PathNode.clone = function (node) { return node.clone(); };
        return PathNode;
    }());

    /**
     * Encapsulate the target (destination) state/params/options of a [[Transition]].
     *
     * This class is frequently used to redirect a transition to a new destination.
     *
     * See:
     *
     * - [[HookResult]]
     * - [[TransitionHookFn]]
     * - [[TransitionService.onStart]]
     *
     * To create a `TargetState`, use [[StateService.target]].
     *
     * ---
     *
     * This class wraps:
     *
     * 1) an identifier for a state
     * 2) a set of parameters
     * 3) and transition options
     * 4) the registered state object (the [[StateDeclaration]])
     *
     * Many UI-Router APIs such as [[StateService.go]] take a [[StateOrName]] argument which can
     * either be a *state object* (a [[StateDeclaration]] or [[StateObject]]) or a *state name* (a string).
     * The `TargetState` class normalizes those options.
     *
     * A `TargetState` may be valid (the state being targeted exists in the registry)
     * or invalid (the state being targeted is not registered).
     */
    var TargetState = /** @class */ (function () {
        /**
         * The TargetState constructor
         *
         * Note: Do not construct a `TargetState` manually.
         * To create a `TargetState`, use the [[StateService.target]] factory method.
         *
         * @param _stateRegistry The StateRegistry to use to look up the _definition
         * @param _identifier An identifier for a state.
         *    Either a fully-qualified state name, or the object used to define the state.
         * @param _params Parameters for the target state
         * @param _options Transition options.
         *
         * @internal
         */
        function TargetState(_stateRegistry, _identifier, _params, _options) {
            this._stateRegistry = _stateRegistry;
            this._identifier = _identifier;
            this._identifier = _identifier;
            this._params = extend({}, _params || {});
            this._options = extend({}, _options || {});
            this._definition = _stateRegistry.matcher.find(_identifier, this._options.relative);
        }
        /** The name of the state this object targets */
        TargetState.prototype.name = function () {
            return (this._definition && this._definition.name) || this._identifier;
        };
        /** The identifier used when creating this TargetState */
        TargetState.prototype.identifier = function () {
            return this._identifier;
        };
        /** The target parameter values */
        TargetState.prototype.params = function () {
            return this._params;
        };
        /** The internal state object (if it was found) */
        TargetState.prototype.$state = function () {
            return this._definition;
        };
        /** The internal state declaration (if it was found) */
        TargetState.prototype.state = function () {
            return this._definition && this._definition.self;
        };
        /** The target options */
        TargetState.prototype.options = function () {
            return this._options;
        };
        /** True if the target state was found */
        TargetState.prototype.exists = function () {
            return !!(this._definition && this._definition.self);
        };
        /** True if the object is valid */
        TargetState.prototype.valid = function () {
            return !this.error();
        };
        /** If the object is invalid, returns the reason why */
        TargetState.prototype.error = function () {
            var base = this.options().relative;
            if (!this._definition && !!base) {
                var stateName = base.name ? base.name : base;
                return "Could not resolve '" + this.name() + "' from state '" + stateName + "'";
            }
            if (!this._definition)
                return "No such state '" + this.name() + "'";
            if (!this._definition.self)
                return "State '" + this.name() + "' has an invalid definition";
        };
        TargetState.prototype.toString = function () {
            return "'" + this.name() + "'" + stringify(this.params());
        };
        /**
         * Returns a copy of this TargetState which targets a different state.
         * The new TargetState has the same parameter values and transition options.
         *
         * @param state The new state that should be targeted
         */
        TargetState.prototype.withState = function (state) {
            return new TargetState(this._stateRegistry, state, this._params, this._options);
        };
        /**
         * Returns a copy of this TargetState, using the specified parameter values.
         *
         * @param params the new parameter values to use
         * @param replace When false (default) the new parameter values will be merged with the current values.
         *                When true the parameter values will be used instead of the current values.
         */
        TargetState.prototype.withParams = function (params, replace) {
            if (replace === void 0) { replace = false; }
            var newParams = replace ? params : extend({}, this._params, params);
            return new TargetState(this._stateRegistry, this._identifier, newParams, this._options);
        };
        /**
         * Returns a copy of this TargetState, using the specified Transition Options.
         *
         * @param options the new options to use
         * @param replace When false (default) the new options will be merged with the current options.
         *                When true the options will be used instead of the current options.
         */
        TargetState.prototype.withOptions = function (options, replace) {
            if (replace === void 0) { replace = false; }
            var newOpts = replace ? options : extend({}, this._options, options);
            return new TargetState(this._stateRegistry, this._identifier, this._params, newOpts);
        };
        /** Returns true if the object has a state property that might be a state or state name */
        TargetState.isDef = function (obj) {
            return obj && obj.state && (isString(obj.state) || (isObject(obj.state) && isString(obj.state.name)));
        };
        return TargetState;
    }());

    /**
     * This class contains functions which convert TargetStates, Nodes and paths from one type to another.
     */
    var PathUtils = /** @class */ (function () {
        function PathUtils() {
        }
        /** Given a PathNode[], create an TargetState */
        PathUtils.makeTargetState = function (registry, path) {
            var state = tail(path).state;
            return new TargetState(registry, state, path.map(prop('paramValues')).reduce(mergeR, {}), {});
        };
        PathUtils.buildPath = function (targetState) {
            var toParams = targetState.params();
            return targetState.$state().path.map(function (state) { return new PathNode(state).applyRawParams(toParams); });
        };
        /** Given a fromPath: PathNode[] and a TargetState, builds a toPath: PathNode[] */
        PathUtils.buildToPath = function (fromPath, targetState) {
            var toPath = PathUtils.buildPath(targetState);
            if (targetState.options().inherit) {
                return PathUtils.inheritParams(fromPath, toPath, Object.keys(targetState.params()));
            }
            return toPath;
        };
        /**
         * Creates ViewConfig objects and adds to nodes.
         *
         * On each [[PathNode]], creates ViewConfig objects from the views: property of the node's state
         */
        PathUtils.applyViewConfigs = function ($view, path, states) {
            // Only apply the viewConfigs to the nodes for the given states
            path
                .filter(function (node) { return inArray(states, node.state); })
                .forEach(function (node) {
                var viewDecls = values(node.state.views || {});
                var subPath = PathUtils.subPath(path, function (n) { return n === node; });
                var viewConfigs = viewDecls.map(function (view) { return $view.createViewConfig(subPath, view); });
                node.views = viewConfigs.reduce(unnestR, []);
            });
        };
        /**
         * Given a fromPath and a toPath, returns a new to path which inherits parameters from the fromPath
         *
         * For a parameter in a node to be inherited from the from path:
         * - The toPath's node must have a matching node in the fromPath (by state).
         * - The parameter name must not be found in the toKeys parameter array.
         *
         * Note: the keys provided in toKeys are intended to be those param keys explicitly specified by some
         * caller, for instance, $state.transitionTo(..., toParams).  If a key was found in toParams,
         * it is not inherited from the fromPath.
         */
        PathUtils.inheritParams = function (fromPath, toPath, toKeys) {
            if (toKeys === void 0) { toKeys = []; }
            function nodeParamVals(path, state) {
                var node = find(path, propEq('state', state));
                return extend({}, node && node.paramValues);
            }
            var noInherit = fromPath
                .map(function (node) { return node.paramSchema; })
                .reduce(unnestR, [])
                .filter(function (param) { return !param.inherit; })
                .map(prop('id'));
            /**
             * Given an [[PathNode]] "toNode", return a new [[PathNode]] with param values inherited from the
             * matching node in fromPath.  Only inherit keys that aren't found in "toKeys" from the node in "fromPath""
             */
            function makeInheritedParamsNode(toNode) {
                // All param values for the node (may include default key/vals, when key was not found in toParams)
                var toParamVals = extend({}, toNode && toNode.paramValues);
                // limited to only those keys found in toParams
                var incomingParamVals = pick(toParamVals, toKeys);
                toParamVals = omit(toParamVals, toKeys);
                var fromParamVals = omit(nodeParamVals(fromPath, toNode.state) || {}, noInherit);
                // extend toParamVals with any fromParamVals, then override any of those those with incomingParamVals
                var ownParamVals = extend(toParamVals, fromParamVals, incomingParamVals);
                return new PathNode(toNode.state).applyRawParams(ownParamVals);
            }
            // The param keys specified by the incoming toParams
            return toPath.map(makeInheritedParamsNode);
        };
        /**
         * Computes the tree changes (entering, exiting) between a fromPath and toPath.
         */
        PathUtils.treeChanges = function (fromPath, toPath, reloadState) {
            var max = Math.min(fromPath.length, toPath.length);
            var keep = 0;
            var nodesMatch = function (node1, node2) { return node1.equals(node2, PathUtils.nonDynamicParams); };
            while (keep < max && fromPath[keep].state !== reloadState && nodesMatch(fromPath[keep], toPath[keep])) {
                keep++;
            }
            /** Given a retained node, return a new node which uses the to node's param values */
            function applyToParams(retainedNode, idx) {
                var cloned = retainedNode.clone();
                cloned.paramValues = toPath[idx].paramValues;
                return cloned;
            }
            var from, retained, exiting, entering, to;
            from = fromPath;
            retained = from.slice(0, keep);
            exiting = from.slice(keep);
            // Create a new retained path (with shallow copies of nodes) which have the params of the toPath mapped
            var retainedWithToParams = retained.map(applyToParams);
            entering = toPath.slice(keep);
            to = retainedWithToParams.concat(entering);
            return { from: from, to: to, retained: retained, retainedWithToParams: retainedWithToParams, exiting: exiting, entering: entering };
        };
        /**
         * Returns a new path which is: the subpath of the first path which matches the second path.
         *
         * The new path starts from root and contains any nodes that match the nodes in the second path.
         * It stops before the first non-matching node.
         *
         * Nodes are compared using their state property and their parameter values.
         * If a `paramsFn` is provided, only the [[Param]] returned by the function will be considered when comparing nodes.
         *
         * @param pathA the first path
         * @param pathB the second path
         * @param paramsFn a function which returns the parameters to consider when comparing
         *
         * @returns an array of PathNodes from the first path which match the nodes in the second path
         */
        PathUtils.matching = function (pathA, pathB, paramsFn) {
            var done = false;
            var tuples = arrayTuples(pathA, pathB);
            return tuples.reduce(function (matching, _a) {
                var nodeA = _a[0], nodeB = _a[1];
                done = done || !nodeA.equals(nodeB, paramsFn);
                return done ? matching : matching.concat(nodeA);
            }, []);
        };
        /**
         * Returns true if two paths are identical.
         *
         * @param pathA
         * @param pathB
         * @param paramsFn a function which returns the parameters to consider when comparing
         * @returns true if the the states and parameter values for both paths are identical
         */
        PathUtils.equals = function (pathA, pathB, paramsFn) {
            return pathA.length === pathB.length && PathUtils.matching(pathA, pathB, paramsFn).length === pathA.length;
        };
        /**
         * Return a subpath of a path, which stops at the first matching node
         *
         * Given an array of nodes, returns a subset of the array starting from the first node,
         * stopping when the first node matches the predicate.
         *
         * @param path a path of [[PathNode]]s
         * @param predicate a [[Predicate]] fn that matches [[PathNode]]s
         * @returns a subpath up to the matching node, or undefined if no match is found
         */
        PathUtils.subPath = function (path, predicate) {
            var node = find(path, predicate);
            var elementIdx = path.indexOf(node);
            return elementIdx === -1 ? undefined : path.slice(0, elementIdx + 1);
        };
        PathUtils.nonDynamicParams = function (node) {
            return node.state.parameters({ inherit: false }).filter(function (param) { return !param.dynamic; });
        };
        /** Gets the raw parameter values from a path */
        PathUtils.paramValues = function (path) { return path.reduce(function (acc, node) { return extend(acc, node.paramValues); }, {}); };
        return PathUtils;
    }());

    var resolvePolicies = {
        when: {
            LAZY: 'LAZY',
            EAGER: 'EAGER',
        },
        async: {
            WAIT: 'WAIT',
            NOWAIT: 'NOWAIT',
        },
    };

    // TODO: explicitly make this user configurable
    var defaultResolvePolicy = {
        when: 'LAZY',
        async: 'WAIT',
    };
    /**
     * The basic building block for the resolve system.
     *
     * Resolvables encapsulate a state's resolve's resolveFn, the resolveFn's declared dependencies, the wrapped (.promise),
     * and the unwrapped-when-complete (.data) result of the resolveFn.
     *
     * Resolvable.get() either retrieves the Resolvable's existing promise, or else invokes resolve() (which invokes the
     * resolveFn) and returns the resulting promise.
     *
     * Resolvable.get() and Resolvable.resolve() both execute within a context path, which is passed as the first
     * parameter to those fns.
     */
    var Resolvable = /** @class */ (function () {
        function Resolvable(arg1, resolveFn, deps, policy, data) {
            this.resolved = false;
            this.promise = undefined;
            if (arg1 instanceof Resolvable) {
                extend(this, arg1);
            }
            else if (isFunction(resolveFn)) {
                if (isNullOrUndefined(arg1))
                    throw new Error('new Resolvable(): token argument is required');
                if (!isFunction(resolveFn))
                    throw new Error('new Resolvable(): resolveFn argument must be a function');
                this.token = arg1;
                this.policy = policy;
                this.resolveFn = resolveFn;
                this.deps = deps || [];
                this.data = data;
                this.resolved = data !== undefined;
                this.promise = this.resolved ? services.$q.when(this.data) : undefined;
            }
            else if (isObject(arg1) && arg1.token && (arg1.hasOwnProperty('resolveFn') || arg1.hasOwnProperty('data'))) {
                var literal = arg1;
                return new Resolvable(literal.token, literal.resolveFn, literal.deps, literal.policy, literal.data);
            }
        }
        Resolvable.prototype.getPolicy = function (state) {
            var thisPolicy = this.policy || {};
            var statePolicy = (state && state.resolvePolicy) || {};
            return {
                when: thisPolicy.when || statePolicy.when || defaultResolvePolicy.when,
                async: thisPolicy.async || statePolicy.async || defaultResolvePolicy.async,
            };
        };
        /**
         * Asynchronously resolve this Resolvable's data
         *
         * Given a ResolveContext that this Resolvable is found in:
         * Wait for this Resolvable's dependencies, then invoke this Resolvable's function
         * and update the Resolvable's state
         */
        Resolvable.prototype.resolve = function (resolveContext, trans) {
            var _this = this;
            var $q = services.$q;
            // Gets all dependencies from ResolveContext and wait for them to be resolved
            var getResolvableDependencies = function () {
                return $q.all(resolveContext.getDependencies(_this).map(function (resolvable) { return resolvable.get(resolveContext, trans); }));
            };
            // Invokes the resolve function passing the resolved dependencies as arguments
            var invokeResolveFn = function (resolvedDeps) { return _this.resolveFn.apply(null, resolvedDeps); };
            var node = resolveContext.findNode(this);
            var state = node && node.state;
            var asyncPolicy = this.getPolicy(state).async;
            var customAsyncPolicy = isFunction(asyncPolicy) ? asyncPolicy : identity;
            // After the final value has been resolved, update the state of the Resolvable
            var applyResolvedValue = function (resolvedValue) {
                _this.data = resolvedValue;
                _this.resolved = true;
                _this.resolveFn = null;
                trace.traceResolvableResolved(_this, trans);
                return _this.data;
            };
            // Sets the promise property first, then getsResolvableDependencies in the context of the promise chain. Always waits one tick.
            return (this.promise = $q
                .when()
                .then(getResolvableDependencies)
                .then(invokeResolveFn)
                .then(customAsyncPolicy)
                .then(applyResolvedValue));
        };
        /**
         * Gets a promise for this Resolvable's data.
         *
         * Fetches the data and returns a promise.
         * Returns the existing promise if it has already been fetched once.
         */
        Resolvable.prototype.get = function (resolveContext, trans) {
            return this.promise || this.resolve(resolveContext, trans);
        };
        Resolvable.prototype.toString = function () {
            return "Resolvable(token: " + stringify(this.token) + ", requires: [" + this.deps.map(stringify) + "])";
        };
        Resolvable.prototype.clone = function () {
            return new Resolvable(this);
        };
        Resolvable.fromData = function (token, data) { return new Resolvable(token, function () { return data; }, null, null, data); };
        return Resolvable;
    }());

    var whens = resolvePolicies.when;
    var ALL_WHENS = [whens.EAGER, whens.LAZY];
    var EAGER_WHENS = [whens.EAGER];
    // tslint:disable-next-line:no-inferrable-types
    var NATIVE_INJECTOR_TOKEN = 'Native Injector';
    /**
     * Encapsulates Dependency Injection for a path of nodes
     *
     * UI-Router states are organized as a tree.
     * A nested state has a path of ancestors to the root of the tree.
     * When a state is being activated, each element in the path is wrapped as a [[PathNode]].
     * A `PathNode` is a stateful object that holds things like parameters and resolvables for the state being activated.
     *
     * The ResolveContext closes over the [[PathNode]]s, and provides DI for the last node in the path.
     */
    var ResolveContext = /** @class */ (function () {
        function ResolveContext(_path) {
            this._path = _path;
        }
        /** Gets all the tokens found in the resolve context, de-duplicated */
        ResolveContext.prototype.getTokens = function () {
            return this._path.reduce(function (acc, node) { return acc.concat(node.resolvables.map(function (r) { return r.token; })); }, []).reduce(uniqR, []);
        };
        /**
         * Gets the Resolvable that matches the token
         *
         * Gets the last Resolvable that matches the token in this context, or undefined.
         * Throws an error if it doesn't exist in the ResolveContext
         */
        ResolveContext.prototype.getResolvable = function (token) {
            var matching = this._path
                .map(function (node) { return node.resolvables; })
                .reduce(unnestR, [])
                .filter(function (r) { return r.token === token; });
            return tail(matching);
        };
        /** Returns the [[ResolvePolicy]] for the given [[Resolvable]] */
        ResolveContext.prototype.getPolicy = function (resolvable) {
            var node = this.findNode(resolvable);
            return resolvable.getPolicy(node.state);
        };
        /**
         * Returns a ResolveContext that includes a portion of this one
         *
         * Given a state, this method creates a new ResolveContext from this one.
         * The new context starts at the first node (root) and stops at the node for the `state` parameter.
         *
         * #### Why
         *
         * When a transition is created, the nodes in the "To Path" are injected from a ResolveContext.
         * A ResolveContext closes over a path of [[PathNode]]s and processes the resolvables.
         * The "To State" can inject values from its own resolvables, as well as those from all its ancestor state's (node's).
         * This method is used to create a narrower context when injecting ancestor nodes.
         *
         * @example
         * `let ABCD = new ResolveContext([A, B, C, D]);`
         *
         * Given a path `[A, B, C, D]`, where `A`, `B`, `C` and `D` are nodes for states `a`, `b`, `c`, `d`:
         * When injecting `D`, `D` should have access to all resolvables from `A`, `B`, `C`, `D`.
         * However, `B` should only be able to access resolvables from `A`, `B`.
         *
         * When resolving for the `B` node, first take the full "To Path" Context `[A,B,C,D]` and limit to the subpath `[A,B]`.
         * `let AB = ABCD.subcontext(a)`
         */
        ResolveContext.prototype.subContext = function (state) {
            return new ResolveContext(PathUtils.subPath(this._path, function (node) { return node.state === state; }));
        };
        /**
         * Adds Resolvables to the node that matches the state
         *
         * This adds a [[Resolvable]] (generally one created on the fly; not declared on a [[StateDeclaration.resolve]] block).
         * The resolvable is added to the node matching the `state` parameter.
         *
         * These new resolvables are not automatically fetched.
         * The calling code should either fetch them, fetch something that depends on them,
         * or rely on [[resolvePath]] being called when some state is being entered.
         *
         * Note: each resolvable's [[ResolvePolicy]] is merged with the state's policy, and the global default.
         *
         * @param newResolvables the new Resolvables
         * @param state Used to find the node to put the resolvable on
         */
        ResolveContext.prototype.addResolvables = function (newResolvables, state) {
            var node = find(this._path, propEq('state', state));
            var keys = newResolvables.map(function (r) { return r.token; });
            node.resolvables = node.resolvables.filter(function (r) { return keys.indexOf(r.token) === -1; }).concat(newResolvables);
        };
        /**
         * Returns a promise for an array of resolved path Element promises
         *
         * @param when
         * @param trans
         * @returns {Promise<any>|any}
         */
        ResolveContext.prototype.resolvePath = function (when, trans) {
            var _this = this;
            if (when === void 0) { when = 'LAZY'; }
            // This option determines which 'when' policy Resolvables we are about to fetch.
            var whenOption = inArray(ALL_WHENS, when) ? when : 'LAZY';
            // If the caller specified EAGER, only the EAGER Resolvables are fetched.
            // if the caller specified LAZY, both EAGER and LAZY Resolvables are fetched.`
            var matchedWhens = whenOption === resolvePolicies.when.EAGER ? EAGER_WHENS : ALL_WHENS;
            // get the subpath to the state argument, if provided
            trace.traceResolvePath(this._path, when, trans);
            var matchesPolicy = function (acceptedVals, whenOrAsync) { return function (resolvable) {
                return inArray(acceptedVals, _this.getPolicy(resolvable)[whenOrAsync]);
            }; };
            // Trigger all the (matching) Resolvables in the path
            // Reduce all the "WAIT" Resolvables into an array
            var promises = this._path.reduce(function (acc, node) {
                var nodeResolvables = node.resolvables.filter(matchesPolicy(matchedWhens, 'when'));
                var nowait = nodeResolvables.filter(matchesPolicy(['NOWAIT'], 'async'));
                var wait = nodeResolvables.filter(not(matchesPolicy(['NOWAIT'], 'async')));
                // For the matching Resolvables, start their async fetch process.
                var subContext = _this.subContext(node.state);
                var getResult = function (r) {
                    return r
                        .get(subContext, trans)
                        // Return a tuple that includes the Resolvable's token
                        .then(function (value) { return ({ token: r.token, value: value }); });
                };
                nowait.forEach(getResult);
                return acc.concat(wait.map(getResult));
            }, []);
            // Wait for all the "WAIT" resolvables
            return services.$q.all(promises);
        };
        ResolveContext.prototype.injector = function () {
            return this._injector || (this._injector = new UIInjectorImpl(this));
        };
        ResolveContext.prototype.findNode = function (resolvable) {
            return find(this._path, function (node) { return inArray(node.resolvables, resolvable); });
        };
        /**
         * Gets the async dependencies of a Resolvable
         *
         * Given a Resolvable, returns its dependencies as a Resolvable[]
         */
        ResolveContext.prototype.getDependencies = function (resolvable) {
            var _this = this;
            var node = this.findNode(resolvable);
            // Find which other resolvables are "visible" to the `resolvable` argument
            // subpath stopping at resolvable's node, or the whole path (if the resolvable isn't in the path)
            var subPath = PathUtils.subPath(this._path, function (x) { return x === node; }) || this._path;
            var availableResolvables = subPath
                .reduce(function (acc, _node) { return acc.concat(_node.resolvables); }, []) // all of subpath's resolvables
                .filter(function (res) { return res !== resolvable; }); // filter out the `resolvable` argument
            var getDependency = function (token) {
                var matching = availableResolvables.filter(function (r) { return r.token === token; });
                if (matching.length)
                    return tail(matching);
                var fromInjector = _this.injector().getNative(token);
                if (isUndefined(fromInjector)) {
                    throw new Error('Could not find Dependency Injection token: ' + stringify(token));
                }
                return new Resolvable(token, function () { return fromInjector; }, [], fromInjector);
            };
            return resolvable.deps.map(getDependency);
        };
        return ResolveContext;
    }());
    /** @internal */
    var UIInjectorImpl = /** @class */ (function () {
        function UIInjectorImpl(context) {
            this.context = context;
            this.native = this.get(NATIVE_INJECTOR_TOKEN) || services.$injector;
        }
        UIInjectorImpl.prototype.get = function (token) {
            var resolvable = this.context.getResolvable(token);
            if (resolvable) {
                if (this.context.getPolicy(resolvable).async === 'NOWAIT') {
                    return resolvable.get(this.context);
                }
                if (!resolvable.resolved) {
                    throw new Error('Resolvable async .get() not complete:' + stringify(resolvable.token));
                }
                return resolvable.data;
            }
            return this.getNative(token);
        };
        UIInjectorImpl.prototype.getAsync = function (token) {
            var resolvable = this.context.getResolvable(token);
            if (resolvable)
                return resolvable.get(this.context);
            return services.$q.when(this.native.get(token));
        };
        UIInjectorImpl.prototype.getNative = function (token) {
            return this.native && this.native.get(token);
        };
        return UIInjectorImpl;
    }());

    var parseUrl = function (url) {
        if (!isString(url))
            return false;
        var root = url.charAt(0) === '^';
        return { val: root ? url.substring(1) : url, root: root };
    };
    function nameBuilder(state) {
        return state.name;
    }
    function selfBuilder(state) {
        state.self.$$state = function () { return state; };
        return state.self;
    }
    function dataBuilder(state) {
        if (state.parent && state.parent.data) {
            state.data = state.self.data = inherit(state.parent.data, state.data);
        }
        return state.data;
    }
    var getUrlBuilder = function ($urlMatcherFactoryProvider, root) {
        return function urlBuilder(stateObject) {
            var stateDec = stateObject.self;
            // For future states, i.e., states whose name ends with `.**`,
            // match anything that starts with the url prefix
            if (stateDec && stateDec.url && stateDec.name && stateDec.name.match(/\.\*\*$/)) {
                var newStateDec = {};
                copy(stateDec, newStateDec);
                newStateDec.url += '{remainder:any}'; // match any path (.*)
                stateDec = newStateDec;
            }
            var parent = stateObject.parent;
            var parsed = parseUrl(stateDec.url);
            var url = !parsed ? stateDec.url : $urlMatcherFactoryProvider.compile(parsed.val, { state: stateDec });
            if (!url)
                return null;
            if (!$urlMatcherFactoryProvider.isMatcher(url))
                throw new Error("Invalid url '" + url + "' in state '" + stateObject + "'");
            return parsed && parsed.root ? url : ((parent && parent.navigable) || root()).url.append(url);
        };
    };
    var getNavigableBuilder = function (isRoot) {
        return function navigableBuilder(state) {
            return !isRoot(state) && state.url ? state : state.parent ? state.parent.navigable : null;
        };
    };
    var getParamsBuilder = function (paramFactory) {
        return function paramsBuilder(state) {
            var makeConfigParam = function (config, id) { return paramFactory.fromConfig(id, null, state.self); };
            var urlParams = (state.url && state.url.parameters({ inherit: false })) || [];
            var nonUrlParams = values(mapObj(omit(state.params || {}, urlParams.map(prop('id'))), makeConfigParam));
            return urlParams
                .concat(nonUrlParams)
                .map(function (p) { return [p.id, p]; })
                .reduce(applyPairs, {});
        };
    };
    function pathBuilder(state) {
        return state.parent ? state.parent.path.concat(state) : /*root*/ [state];
    }
    function includesBuilder(state) {
        var includes = state.parent ? extend({}, state.parent.includes) : {};
        includes[state.name] = true;
        return includes;
    }
    /**
     * This is a [[StateBuilder.builder]] function for the `resolve:` block on a [[StateDeclaration]].
     *
     * When the [[StateBuilder]] builds a [[StateObject]] object from a raw [[StateDeclaration]], this builder
     * validates the `resolve` property and converts it to a [[Resolvable]] array.
     *
     * resolve: input value can be:
     *
     * {
     *   // analyzed but not injected
     *   myFooResolve: function() { return "myFooData"; },
     *
     *   // function.toString() parsed, "DependencyName" dep as string (not min-safe)
     *   myBarResolve: function(DependencyName) { return DependencyName.fetchSomethingAsPromise() },
     *
     *   // Array split; "DependencyName" dep as string
     *   myBazResolve: [ "DependencyName", function(dep) { return dep.fetchSomethingAsPromise() },
     *
     *   // Array split; DependencyType dep as token (compared using ===)
     *   myQuxResolve: [ DependencyType, function(dep) { return dep.fetchSometingAsPromise() },
     *
     *   // val.$inject used as deps
     *   // where:
     *   //     corgeResolve.$inject = ["DependencyName"];
     *   //     function corgeResolve(dep) { dep.fetchSometingAsPromise() }
     *   // then "DependencyName" dep as string
     *   myCorgeResolve: corgeResolve,
     *
     *  // inject service by name
     *  // When a string is found, desugar creating a resolve that injects the named service
     *   myGraultResolve: "SomeService"
     * }
     *
     * or:
     *
     * [
     *   new Resolvable("myFooResolve", function() { return "myFooData" }),
     *   new Resolvable("myBarResolve", function(dep) { return dep.fetchSomethingAsPromise() }, [ "DependencyName" ]),
     *   { provide: "myBazResolve", useFactory: function(dep) { dep.fetchSomethingAsPromise() }, deps: [ "DependencyName" ] }
     * ]
     */
    function resolvablesBuilder(state) {
        /** convert resolve: {} and resolvePolicy: {} objects to an array of tuples */
        var objects2Tuples = function (resolveObj, resolvePolicies) {
            return Object.keys(resolveObj || {}).map(function (token) { return ({
                token: token,
                val: resolveObj[token],
                deps: undefined,
                policy: resolvePolicies[token],
            }); });
        };
        /** fetch DI annotations from a function or ng1-style array */
        var annotate = function (fn) {
            var $injector = services.$injector;
            // ng1 doesn't have an $injector until runtime.
            // If the $injector doesn't exist, use "deferred" literal as a
            // marker indicating they should be annotated when runtime starts
            return fn['$inject'] || ($injector && $injector.annotate(fn, $injector.strictDi)) || 'deferred';
        };
        /** true if the object has both `token` and `resolveFn`, and is probably a [[ResolveLiteral]] */
        var isResolveLiteral = function (obj) { return !!(obj.token && obj.resolveFn); };
        /** true if the object looks like a provide literal, or a ng2 Provider */
        var isLikeNg2Provider = function (obj) {
            return !!((obj.provide || obj.token) && (obj.useValue || obj.useFactory || obj.useExisting || obj.useClass));
        };
        /** true if the object looks like a tuple from obj2Tuples */
        var isTupleFromObj = function (obj) {
            return !!(obj && obj.val && (isString(obj.val) || isArray(obj.val) || isFunction(obj.val)));
        };
        /** extracts the token from a Provider or provide literal */
        var getToken = function (p) { return p.provide || p.token; };
        // prettier-ignore: Given a literal resolve or provider object, returns a Resolvable
        var literal2Resolvable = pattern([
            [prop('resolveFn'), function (p) { return new Resolvable(getToken(p), p.resolveFn, p.deps, p.policy); }],
            [prop('useFactory'), function (p) { return new Resolvable(getToken(p), p.useFactory, p.deps || p.dependencies, p.policy); }],
            [prop('useClass'), function (p) { return new Resolvable(getToken(p), function () { return new p.useClass(); }, [], p.policy); }],
            [prop('useValue'), function (p) { return new Resolvable(getToken(p), function () { return p.useValue; }, [], p.policy, p.useValue); }],
            [prop('useExisting'), function (p) { return new Resolvable(getToken(p), identity, [p.useExisting], p.policy); }],
        ]);
        // prettier-ignore
        var tuple2Resolvable = pattern([
            [pipe(prop('val'), isString), function (tuple) { return new Resolvable(tuple.token, identity, [tuple.val], tuple.policy); }],
            [pipe(prop('val'), isArray), function (tuple) { return new Resolvable(tuple.token, tail(tuple.val), tuple.val.slice(0, -1), tuple.policy); }],
            [pipe(prop('val'), isFunction), function (tuple) { return new Resolvable(tuple.token, tuple.val, annotate(tuple.val), tuple.policy); }],
        ]);
        // prettier-ignore
        var item2Resolvable = pattern([
            [is(Resolvable), function (r) { return r; }],
            [isResolveLiteral, literal2Resolvable],
            [isLikeNg2Provider, literal2Resolvable],
            [isTupleFromObj, tuple2Resolvable],
            [val(true), function (obj) { throw new Error('Invalid resolve value: ' + stringify(obj)); },],
        ]);
        // If resolveBlock is already an array, use it as-is.
        // Otherwise, assume it's an object and convert to an Array of tuples
        var decl = state.resolve;
        var items = isArray(decl) ? decl : objects2Tuples(decl, state.resolvePolicy || {});
        return items.map(item2Resolvable);
    }
    /**
     * A internal global service
     *
     * StateBuilder is a factory for the internal [[StateObject]] objects.
     *
     * When you register a state with the [[StateRegistry]], you register a plain old javascript object which
     * conforms to the [[StateDeclaration]] interface.  This factory takes that object and builds the corresponding
     * [[StateObject]] object, which has an API and is used internally.
     *
     * Custom properties or API may be added to the internal [[StateObject]] object by registering a decorator function
     * using the [[builder]] method.
     */
    var StateBuilder = /** @class */ (function () {
        function StateBuilder(matcher, urlMatcherFactory) {
            this.matcher = matcher;
            var self = this;
            var root = function () { return matcher.find(''); };
            var isRoot = function (state) { return state.name === ''; };
            function parentBuilder(state) {
                if (isRoot(state))
                    return null;
                return matcher.find(self.parentName(state)) || root();
            }
            this.builders = {
                name: [nameBuilder],
                self: [selfBuilder],
                parent: [parentBuilder],
                data: [dataBuilder],
                // Build a URLMatcher if necessary, either via a relative or absolute URL
                url: [getUrlBuilder(urlMatcherFactory, root)],
                // Keep track of the closest ancestor state that has a URL (i.e. is navigable)
                navigable: [getNavigableBuilder(isRoot)],
                params: [getParamsBuilder(urlMatcherFactory.paramFactory)],
                // Each framework-specific ui-router implementation should define its own `views` builder
                // e.g., src/ng1/statebuilders/views.ts
                views: [],
                // Keep a full path from the root down to this state as this is needed for state activation.
                path: [pathBuilder],
                // Speed up $state.includes() as it's used a lot
                includes: [includesBuilder],
                resolvables: [resolvablesBuilder],
            };
        }
        StateBuilder.prototype.builder = function (name, fn) {
            var builders = this.builders;
            var array = builders[name] || [];
            // Backwards compat: if only one builder exists, return it, else return whole arary.
            if (isString(name) && !isDefined(fn))
                return array.length > 1 ? array : array[0];
            if (!isString(name) || !isFunction(fn))
                return;
            builders[name] = array;
            builders[name].push(fn);
            return function () { return builders[name].splice(builders[name].indexOf(fn, 1)) && null; };
        };
        /**
         * Builds all of the properties on an essentially blank State object, returning a State object which has all its
         * properties and API built.
         *
         * @param state an uninitialized State object
         * @returns the built State object
         */
        StateBuilder.prototype.build = function (state) {
            var _a = this, matcher = _a.matcher, builders = _a.builders;
            var parent = this.parentName(state);
            if (parent && !matcher.find(parent, undefined, false)) {
                return null;
            }
            for (var key in builders) {
                if (!builders.hasOwnProperty(key))
                    continue;
                var chain = builders[key].reduce(function (parentFn, step) { return function (_state) { return step(_state, parentFn); }; }, noop);
                state[key] = chain(state);
            }
            return state;
        };
        StateBuilder.prototype.parentName = function (state) {
            // name = 'foo.bar.baz.**'
            var name = state.name || '';
            // segments = ['foo', 'bar', 'baz', '.**']
            var segments = name.split('.');
            // segments = ['foo', 'bar', 'baz']
            var lastSegment = segments.pop();
            // segments = ['foo', 'bar'] (ignore .** segment for future states)
            if (lastSegment === '**')
                segments.pop();
            if (segments.length) {
                if (state.parent) {
                    throw new Error("States that specify the 'parent:' property should not have a '.' in their name (" + name + ")");
                }
                // 'foo.bar'
                return segments.join('.');
            }
            if (!state.parent)
                return '';
            return isString(state.parent) ? state.parent : state.parent.name;
        };
        StateBuilder.prototype.name = function (state) {
            var name = state.name;
            if (name.indexOf('.') !== -1 || !state.parent)
                return name;
            var parentName = isString(state.parent) ? state.parent : state.parent.name;
            return parentName ? parentName + '.' + name : name;
        };
        return StateBuilder;
    }());

    /**
     * Internal representation of a UI-Router state.
     *
     * Instances of this class are created when a [[StateDeclaration]] is registered with the [[StateRegistry]].
     *
     * A registered [[StateDeclaration]] is augmented with a getter ([[StateDeclaration.$$state]]) which returns the corresponding [[StateObject]] object.
     *
     * This class prototypally inherits from the corresponding [[StateDeclaration]].
     * Each of its own properties (i.e., `hasOwnProperty`) are built using builders from the [[StateBuilder]].
     */
    var StateObject = /** @class */ (function () {
        /** @deprecated use State.create() */
        function StateObject(config) {
            return StateObject.create(config || {});
        }
        /**
         * Create a state object to put the private/internal implementation details onto.
         * The object's prototype chain looks like:
         * (Internal State Object) -> (Copy of State.prototype) -> (State Declaration object) -> (State Declaration's prototype...)
         *
         * @param stateDecl the user-supplied State Declaration
         * @returns {StateObject} an internal State object
         */
        StateObject.create = function (stateDecl) {
            stateDecl = StateObject.isStateClass(stateDecl) ? new stateDecl() : stateDecl;
            var state = inherit(inherit(stateDecl, StateObject.prototype));
            stateDecl.$$state = function () { return state; };
            state.self = stateDecl;
            state.__stateObjectCache = {
                nameGlob: Glob.fromString(state.name),
            };
            return state;
        };
        /**
         * Returns true if the provided parameter is the same state.
         *
         * Compares the identity of the state against the passed value, which is either an object
         * reference to the actual `State` instance, the original definition object passed to
         * `$stateProvider.state()`, or the fully-qualified name.
         *
         * @param ref Can be one of (a) a `State` instance, (b) an object that was passed
         *        into `$stateProvider.state()`, (c) the fully-qualified name of a state as a string.
         * @returns Returns `true` if `ref` matches the current `State` instance.
         */
        StateObject.prototype.is = function (ref) {
            return this === ref || this.self === ref || this.fqn() === ref;
        };
        /**
         * @deprecated this does not properly handle dot notation
         * @returns Returns a dot-separated name of the state.
         */
        StateObject.prototype.fqn = function () {
            if (!this.parent || !(this.parent instanceof this.constructor))
                return this.name;
            var name = this.parent.fqn();
            return name ? name + '.' + this.name : this.name;
        };
        /**
         * Returns the root node of this state's tree.
         *
         * @returns The root of this state's tree.
         */
        StateObject.prototype.root = function () {
            return (this.parent && this.parent.root()) || this;
        };
        /**
         * Gets the state's `Param` objects
         *
         * Gets the list of [[Param]] objects owned by the state.
         * If `opts.inherit` is true, it also includes the ancestor states' [[Param]] objects.
         * If `opts.matchingKeys` exists, returns only `Param`s whose `id` is a key on the `matchingKeys` object
         *
         * @param opts options
         */
        StateObject.prototype.parameters = function (opts) {
            opts = defaults(opts, { inherit: true, matchingKeys: null });
            var inherited = (opts.inherit && this.parent && this.parent.parameters()) || [];
            return inherited
                .concat(values(this.params))
                .filter(function (param) { return !opts.matchingKeys || opts.matchingKeys.hasOwnProperty(param.id); });
        };
        /**
         * Returns a single [[Param]] that is owned by the state
         *
         * If `opts.inherit` is true, it also searches the ancestor states` [[Param]]s.
         * @param id the name of the [[Param]] to return
         * @param opts options
         */
        StateObject.prototype.parameter = function (id, opts) {
            if (opts === void 0) { opts = {}; }
            return ((this.url && this.url.parameter(id, opts)) ||
                find(values(this.params), propEq('id', id)) ||
                (opts.inherit && this.parent && this.parent.parameter(id)));
        };
        StateObject.prototype.toString = function () {
            return this.fqn();
        };
        /** Predicate which returns true if the object is an class with @State() decorator */
        StateObject.isStateClass = function (stateDecl) {
            return isFunction(stateDecl) && stateDecl['__uiRouterState'] === true;
        };
        /** Predicate which returns true if the object is a [[StateDeclaration]] object */
        StateObject.isStateDeclaration = function (obj) { return isFunction(obj['$$state']); };
        /** Predicate which returns true if the object is an internal [[StateObject]] object */
        StateObject.isState = function (obj) { return isObject(obj['__stateObjectCache']); };
        return StateObject;
    }());

    var StateMatcher = /** @class */ (function () {
        function StateMatcher(_states) {
            this._states = _states;
        }
        StateMatcher.prototype.isRelative = function (stateName) {
            stateName = stateName || '';
            return stateName.indexOf('.') === 0 || stateName.indexOf('^') === 0;
        };
        StateMatcher.prototype.find = function (stateOrName, base, matchGlob) {
            if (matchGlob === void 0) { matchGlob = true; }
            if (!stateOrName && stateOrName !== '')
                return undefined;
            var isStr = isString(stateOrName);
            var name = isStr ? stateOrName : stateOrName.name;
            if (this.isRelative(name))
                name = this.resolvePath(name, base);
            var state = this._states[name];
            if (state && (isStr || (!isStr && (state === stateOrName || state.self === stateOrName)))) {
                return state;
            }
            else if (isStr && matchGlob) {
                var _states = values(this._states);
                var matches = _states.filter(function (_state) { return _state.__stateObjectCache.nameGlob && _state.__stateObjectCache.nameGlob.matches(name); });
                if (matches.length > 1) {
                    safeConsole.error("stateMatcher.find: Found multiple matches for " + name + " using glob: ", matches.map(function (match) { return match.name; }));
                }
                return matches[0];
            }
            return undefined;
        };
        StateMatcher.prototype.resolvePath = function (name, base) {
            if (!base)
                throw new Error("No reference point given for path '" + name + "'");
            var baseState = this.find(base);
            var splitName = name.split('.');
            var pathLength = splitName.length;
            var i = 0, current = baseState;
            for (; i < pathLength; i++) {
                if (splitName[i] === '' && i === 0) {
                    current = baseState;
                    continue;
                }
                if (splitName[i] === '^') {
                    if (!current.parent)
                        throw new Error("Path '" + name + "' not valid for state '" + baseState.name + "'");
                    current = current.parent;
                    continue;
                }
                break;
            }
            var relName = splitName.slice(i).join('.');
            return current.name + (current.name && relName ? '.' : '') + relName;
        };
        return StateMatcher;
    }());

    var StateQueueManager = /** @class */ (function () {
        function StateQueueManager(router, states, builder, listeners) {
            this.router = router;
            this.states = states;
            this.builder = builder;
            this.listeners = listeners;
            this.queue = [];
        }
        StateQueueManager.prototype.dispose = function () {
            this.queue = [];
        };
        StateQueueManager.prototype.register = function (stateDecl) {
            var queue = this.queue;
            var state = StateObject.create(stateDecl);
            var name = state.name;
            if (!isString(name))
                throw new Error('State must have a valid name');
            if (this.states.hasOwnProperty(name) || inArray(queue.map(prop('name')), name))
                throw new Error("State '" + name + "' is already defined");
            queue.push(state);
            this.flush();
            return state;
        };
        StateQueueManager.prototype.flush = function () {
            var _this = this;
            var _a = this, queue = _a.queue, states = _a.states, builder = _a.builder;
            var registered = [], // states that got registered
            orphans = [], // states that don't yet have a parent registered
            previousQueueLength = {}; // keep track of how long the queue when an orphan was first encountered
            var getState = function (name) { return _this.states.hasOwnProperty(name) && _this.states[name]; };
            var notifyListeners = function () {
                if (registered.length) {
                    _this.listeners.forEach(function (listener) {
                        return listener('registered', registered.map(function (s) { return s.self; }));
                    });
                }
            };
            while (queue.length > 0) {
                var state = queue.shift();
                var name_1 = state.name;
                var result = builder.build(state);
                var orphanIdx = orphans.indexOf(state);
                if (result) {
                    var existingState = getState(name_1);
                    if (existingState && existingState.name === name_1) {
                        throw new Error("State '" + name_1 + "' is already defined");
                    }
                    var existingFutureState = getState(name_1 + '.**');
                    if (existingFutureState) {
                        // Remove future state of the same name
                        this.router.stateRegistry.deregister(existingFutureState);
                    }
                    states[name_1] = state;
                    this.attachRoute(state);
                    if (orphanIdx >= 0)
                        orphans.splice(orphanIdx, 1);
                    registered.push(state);
                    continue;
                }
                var prev = previousQueueLength[name_1];
                previousQueueLength[name_1] = queue.length;
                if (orphanIdx >= 0 && prev === queue.length) {
                    // Wait until two consecutive iterations where no additional states were dequeued successfully.
                    // throw new Error(`Cannot register orphaned state '${name}'`);
                    queue.push(state);
                    notifyListeners();
                    return states;
                }
                else if (orphanIdx < 0) {
                    orphans.push(state);
                }
                queue.push(state);
            }
            notifyListeners();
            return states;
        };
        StateQueueManager.prototype.attachRoute = function (state) {
            if (state.abstract || !state.url)
                return;
            var rulesApi = this.router.urlService.rules;
            rulesApi.rule(rulesApi.urlRuleFactory.create(state));
        };
        return StateQueueManager;
    }());

    /**
     * A registry for all of the application's [[StateDeclaration]]s
     *
     * This API is found at `router.stateRegistry` ([[UIRouter.stateRegistry]])
     */
    var StateRegistry = /** @class */ (function () {
        /** @internal */
        function StateRegistry(router) {
            this.router = router;
            this.states = {};
            /** @internal */
            this.listeners = [];
            this.matcher = new StateMatcher(this.states);
            this.builder = new StateBuilder(this.matcher, router.urlMatcherFactory);
            this.stateQueue = new StateQueueManager(router, this.states, this.builder, this.listeners);
            this._registerRoot();
        }
        /** @internal */
        StateRegistry.prototype._registerRoot = function () {
            var rootStateDef = {
                name: '',
                url: '^',
                views: null,
                params: {
                    '#': { value: null, type: 'hash', dynamic: true },
                },
                abstract: true,
            };
            var _root = (this._root = this.stateQueue.register(rootStateDef));
            _root.navigable = null;
        };
        /** @internal */
        StateRegistry.prototype.dispose = function () {
            var _this = this;
            this.stateQueue.dispose();
            this.listeners = [];
            this.get().forEach(function (state) { return _this.get(state) && _this.deregister(state); });
        };
        /**
         * Listen for a State Registry events
         *
         * Adds a callback that is invoked when states are registered or deregistered with the StateRegistry.
         *
         * #### Example:
         * ```js
         * let allStates = registry.get();
         *
         * // Later, invoke deregisterFn() to remove the listener
         * let deregisterFn = registry.onStatesChanged((event, states) => {
         *   switch(event) {
         *     case: 'registered':
         *       states.forEach(state => allStates.push(state));
         *       break;
         *     case: 'deregistered':
         *       states.forEach(state => {
         *         let idx = allStates.indexOf(state);
         *         if (idx !== -1) allStates.splice(idx, 1);
         *       });
         *       break;
         *   }
         * });
         * ```
         *
         * @param listener a callback function invoked when the registered states changes.
         *        The function receives two parameters, `event` and `state`.
         *        See [[StateRegistryListener]]
         * @return a function that deregisters the listener
         */
        StateRegistry.prototype.onStatesChanged = function (listener) {
            this.listeners.push(listener);
            return function deregisterListener() {
                removeFrom(this.listeners)(listener);
            }.bind(this);
        };
        /**
         * Gets the implicit root state
         *
         * Gets the root of the state tree.
         * The root state is implicitly created by UI-Router.
         * Note: this returns the internal [[StateObject]] representation, not a [[StateDeclaration]]
         *
         * @return the root [[StateObject]]
         */
        StateRegistry.prototype.root = function () {
            return this._root;
        };
        /**
         * Adds a state to the registry
         *
         * Registers a [[StateDeclaration]] or queues it for registration.
         *
         * Note: a state will be queued if the state's parent isn't yet registered.
         *
         * @param stateDefinition the definition of the state to register.
         * @returns the internal [[StateObject]] object.
         *          If the state was successfully registered, then the object is fully built (See: [[StateBuilder]]).
         *          If the state was only queued, then the object is not fully built.
         */
        StateRegistry.prototype.register = function (stateDefinition) {
            return this.stateQueue.register(stateDefinition);
        };
        /** @internal */
        StateRegistry.prototype._deregisterTree = function (state) {
            var _this = this;
            var all = this.get().map(function (s) { return s.$$state(); });
            var getChildren = function (states) {
                var _children = all.filter(function (s) { return states.indexOf(s.parent) !== -1; });
                return _children.length === 0 ? _children : _children.concat(getChildren(_children));
            };
            var children = getChildren([state]);
            var deregistered = [state].concat(children).reverse();
            deregistered.forEach(function (_state) {
                var rulesApi = _this.router.urlService.rules;
                // Remove URL rule
                rulesApi
                    .rules()
                    .filter(propEq('state', _state))
                    .forEach(function (rule) { return rulesApi.removeRule(rule); });
                // Remove state from registry
                delete _this.states[_state.name];
            });
            return deregistered;
        };
        /**
         * Removes a state from the registry
         *
         * This removes a state from the registry.
         * If the state has children, they are are also removed from the registry.
         *
         * @param stateOrName the state's name or object representation
         * @returns {StateObject[]} a list of removed states
         */
        StateRegistry.prototype.deregister = function (stateOrName) {
            var _state = this.get(stateOrName);
            if (!_state)
                throw new Error("Can't deregister state; not found: " + stateOrName);
            var deregisteredStates = this._deregisterTree(_state.$$state());
            this.listeners.forEach(function (listener) {
                return listener('deregistered', deregisteredStates.map(function (s) { return s.self; }));
            });
            return deregisteredStates;
        };
        StateRegistry.prototype.get = function (stateOrName, base) {
            var _this = this;
            if (arguments.length === 0)
                return Object.keys(this.states).map(function (name) { return _this.states[name].self; });
            var found = this.matcher.find(stateOrName, base);
            return (found && found.self) || null;
        };
        /**
         * Registers a [[BuilderFunction]] for a specific [[StateObject]] property (e.g., `parent`, `url`, or `path`).
         * More than one BuilderFunction can be registered for a given property.
         *
         * The BuilderFunction(s) will be used to define the property on any subsequently built [[StateObject]] objects.
         *
         * @param property The name of the State property being registered for.
         * @param builderFunction The BuilderFunction which will be used to build the State property
         * @returns a function which deregisters the BuilderFunction
         */
        StateRegistry.prototype.decorator = function (property, builderFunction) {
            return this.builder.builder(property, builderFunction);
        };
        return StateRegistry;
    }());

    (function (TransitionHookPhase) {
        TransitionHookPhase[TransitionHookPhase["CREATE"] = 0] = "CREATE";
        TransitionHookPhase[TransitionHookPhase["BEFORE"] = 1] = "BEFORE";
        TransitionHookPhase[TransitionHookPhase["RUN"] = 2] = "RUN";
        TransitionHookPhase[TransitionHookPhase["SUCCESS"] = 3] = "SUCCESS";
        TransitionHookPhase[TransitionHookPhase["ERROR"] = 4] = "ERROR";
    })(exports.TransitionHookPhase || (exports.TransitionHookPhase = {}));

    (function (TransitionHookScope) {
        TransitionHookScope[TransitionHookScope["TRANSITION"] = 0] = "TRANSITION";
        TransitionHookScope[TransitionHookScope["STATE"] = 1] = "STATE";
    })(exports.TransitionHookScope || (exports.TransitionHookScope = {}));

    var defaultOptions = {
        current: noop,
        transition: null,
        traceData: {},
        bind: null,
    };
    var TransitionHook = /** @class */ (function () {
        function TransitionHook(transition, stateContext, registeredHook, options) {
            var _this = this;
            this.transition = transition;
            this.stateContext = stateContext;
            this.registeredHook = registeredHook;
            this.options = options;
            this.isSuperseded = function () { return _this.type.hookPhase === exports.TransitionHookPhase.RUN && !_this.options.transition.isActive(); };
            this.options = defaults(options, defaultOptions);
            this.type = registeredHook.eventType;
        }
        /**
         * Chains together an array of TransitionHooks.
         *
         * Given a list of [[TransitionHook]] objects, chains them together.
         * Each hook is invoked after the previous one completes.
         *
         * #### Example:
         * ```js
         * var hooks: TransitionHook[] = getHooks();
         * let promise: Promise<any> = TransitionHook.chain(hooks);
         *
         * promise.then(handleSuccess, handleError);
         * ```
         *
         * @param hooks the list of hooks to chain together
         * @param waitFor if provided, the chain is `.then()`'ed off this promise
         * @returns a `Promise` for sequentially invoking the hooks (in order)
         */
        TransitionHook.chain = function (hooks, waitFor) {
            // Chain the next hook off the previous
            var createHookChainR = function (prev, nextHook) { return prev.then(function () { return nextHook.invokeHook(); }); };
            return hooks.reduce(createHookChainR, waitFor || services.$q.when());
        };
        /**
         * Invokes all the provided TransitionHooks, in order.
         * Each hook's return value is checked.
         * If any hook returns a promise, then the rest of the hooks are chained off that promise, and the promise is returned.
         * If no hook returns a promise, then all hooks are processed synchronously.
         *
         * @param hooks the list of TransitionHooks to invoke
         * @param doneCallback a callback that is invoked after all the hooks have successfully completed
         *
         * @returns a promise for the async result, or the result of the callback
         */
        TransitionHook.invokeHooks = function (hooks, doneCallback) {
            for (var idx = 0; idx < hooks.length; idx++) {
                var hookResult = hooks[idx].invokeHook();
                if (isPromise(hookResult)) {
                    var remainingHooks = hooks.slice(idx + 1);
                    return TransitionHook.chain(remainingHooks, hookResult).then(doneCallback);
                }
            }
            return doneCallback();
        };
        /**
         * Run all TransitionHooks, ignoring their return value.
         */
        TransitionHook.runAllHooks = function (hooks) {
            hooks.forEach(function (hook) { return hook.invokeHook(); });
        };
        TransitionHook.prototype.logError = function (err) {
            this.transition.router.stateService.defaultErrorHandler()(err);
        };
        TransitionHook.prototype.invokeHook = function () {
            var _this = this;
            var hook = this.registeredHook;
            if (hook._deregistered)
                return;
            var notCurrent = this.getNotCurrentRejection();
            if (notCurrent)
                return notCurrent;
            var options = this.options;
            trace.traceHookInvocation(this, this.transition, options);
            var invokeCallback = function () { return hook.callback.call(options.bind, _this.transition, _this.stateContext); };
            var normalizeErr = function (err) { return Rejection.normalize(err).toPromise(); };
            var handleError = function (err) { return hook.eventType.getErrorHandler(_this)(err); };
            var handleResult = function (result) { return hook.eventType.getResultHandler(_this)(result); };
            try {
                var result = invokeCallback();
                if (!this.type.synchronous && isPromise(result)) {
                    return result.catch(normalizeErr).then(handleResult, handleError);
                }
                else {
                    return handleResult(result);
                }
            }
            catch (err) {
                // If callback throws (synchronously)
                return handleError(Rejection.normalize(err));
            }
            finally {
                if (hook.invokeLimit && ++hook.invokeCount >= hook.invokeLimit) {
                    hook.deregister();
                }
            }
        };
        /**
         * This method handles the return value of a Transition Hook.
         *
         * A hook can return false (cancel), a TargetState (redirect),
         * or a promise (which may later resolve to false or a redirect)
         *
         * This also handles "transition superseded" -- when a new transition
         * was started while the hook was still running
         */
        TransitionHook.prototype.handleHookResult = function (result) {
            var _this = this;
            var notCurrent = this.getNotCurrentRejection();
            if (notCurrent)
                return notCurrent;
            // Hook returned a promise
            if (isPromise(result)) {
                // Wait for the promise, then reprocess with the resulting value
                return result.then(function (val) { return _this.handleHookResult(val); });
            }
            trace.traceHookResult(result, this.transition, this.options);
            // Hook returned false
            if (result === false) {
                // Abort this Transition
                return Rejection.aborted('Hook aborted transition').toPromise();
            }
            var isTargetState = is(TargetState);
            // hook returned a TargetState
            if (isTargetState(result)) {
                // Halt the current Transition and redirect (a new Transition) to the TargetState.
                return Rejection.redirected(result).toPromise();
            }
        };
        /**
         * Return a Rejection promise if the transition is no longer current due
         * to a stopped router (disposed), or a new transition has started and superseded this one.
         */
        TransitionHook.prototype.getNotCurrentRejection = function () {
            var router = this.transition.router;
            // The router is stopped
            if (router._disposed) {
                return Rejection.aborted("UIRouter instance #" + router.$id + " has been stopped (disposed)").toPromise();
            }
            if (this.transition._aborted) {
                return Rejection.aborted().toPromise();
            }
            // This transition is no longer current.
            // Another transition started while this hook was still running.
            if (this.isSuperseded()) {
                // Abort this transition
                return Rejection.superseded(this.options.current()).toPromise();
            }
        };
        TransitionHook.prototype.toString = function () {
            var _a = this, options = _a.options, registeredHook = _a.registeredHook;
            var event = parse('traceData.hookType')(options) || 'internal', context = parse('traceData.context.state.name')(options) || parse('traceData.context')(options) || 'unknown', name = fnToString(registeredHook.callback);
            return event + " context: " + context + ", " + maxLength(200, name);
        };
        /**
         * These GetResultHandler(s) are used by [[invokeHook]] below
         * Each HookType chooses a GetResultHandler (See: [[TransitionService._defineCoreEvents]])
         */
        TransitionHook.HANDLE_RESULT = function (hook) { return function (result) {
            return hook.handleHookResult(result);
        }; };
        /**
         * If the result is a promise rejection, log it.
         * Otherwise, ignore the result.
         */
        TransitionHook.LOG_REJECTED_RESULT = function (hook) { return function (result) {
            isPromise(result) && result.catch(function (err) { return hook.logError(Rejection.normalize(err)); });
            return undefined;
        }; };
        /**
         * These GetErrorHandler(s) are used by [[invokeHook]] below
         * Each HookType chooses a GetErrorHandler (See: [[TransitionService._defineCoreEvents]])
         */
        TransitionHook.LOG_ERROR = function (hook) { return function (error) { return hook.logError(error); }; };
        TransitionHook.REJECT_ERROR = function (hook) { return function (error) { return silentRejection(error); }; };
        TransitionHook.THROW_ERROR = function (hook) { return function (error) {
            throw error;
        }; };
        return TransitionHook;
    }());

    /**
     * Determines if the given state matches the matchCriteria
     *
     * @internal
     *
     * @param state a State Object to test against
     * @param criterion
     * - If a string, matchState uses the string as a glob-matcher against the state name
     * - If an array (of strings), matchState uses each string in the array as a glob-matchers against the state name
     *   and returns a positive match if any of the globs match.
     * - If a function, matchState calls the function with the state and returns true if the function's result is truthy.
     * @returns {boolean}
     */
    function matchState(state, criterion, transition) {
        var toMatch = isString(criterion) ? [criterion] : criterion;
        function matchGlobs(_state) {
            var globStrings = toMatch;
            for (var i = 0; i < globStrings.length; i++) {
                var glob = new Glob(globStrings[i]);
                if ((glob && glob.matches(_state.name)) || (!glob && globStrings[i] === _state.name)) {
                    return true;
                }
            }
            return false;
        }
        var matchFn = (isFunction(toMatch) ? toMatch : matchGlobs);
        return !!matchFn(state, transition);
    }
    /**
     * The registration data for a registered transition hook
     */
    var RegisteredHook = /** @class */ (function () {
        function RegisteredHook(tranSvc, eventType, callback, matchCriteria, removeHookFromRegistry, options) {
            if (options === void 0) { options = {}; }
            this.tranSvc = tranSvc;
            this.eventType = eventType;
            this.callback = callback;
            this.matchCriteria = matchCriteria;
            this.removeHookFromRegistry = removeHookFromRegistry;
            this.invokeCount = 0;
            this._deregistered = false;
            this.priority = options.priority || 0;
            this.bind = options.bind || null;
            this.invokeLimit = options.invokeLimit;
        }
        /**
         * Gets the matching [[PathNode]]s
         *
         * Given an array of [[PathNode]]s, and a [[HookMatchCriterion]], returns an array containing
         * the [[PathNode]]s that the criteria matches, or `null` if there were no matching nodes.
         *
         * Returning `null` is significant to distinguish between the default
         * "match-all criterion value" of `true` compared to a `() => true` function,
         * when the nodes is an empty array.
         *
         * This is useful to allow a transition match criteria of `entering: true`
         * to still match a transition, even when `entering === []`.  Contrast that
         * with `entering: (state) => true` which only matches when a state is actually
         * being entered.
         */
        RegisteredHook.prototype._matchingNodes = function (nodes, criterion, transition) {
            if (criterion === true)
                return nodes;
            var matching = nodes.filter(function (node) { return matchState(node.state, criterion, transition); });
            return matching.length ? matching : null;
        };
        /**
         * Gets the default match criteria (all `true`)
         *
         * Returns an object which has all the criteria match paths as keys and `true` as values, i.e.:
         *
         * ```js
         * {
         *   to: true,
         *   from: true,
         *   entering: true,
         *   exiting: true,
         *   retained: true,
         * }
         */
        RegisteredHook.prototype._getDefaultMatchCriteria = function () {
            return mapObj(this.tranSvc._pluginapi._getPathTypes(), function () { return true; });
        };
        /**
         * Gets matching nodes as [[IMatchingNodes]]
         *
         * Create a IMatchingNodes object from the TransitionHookTypes that is roughly equivalent to:
         *
         * ```js
         * let matches: IMatchingNodes = {
         *   to:       _matchingNodes([tail(treeChanges.to)],   mc.to),
         *   from:     _matchingNodes([tail(treeChanges.from)], mc.from),
         *   exiting:  _matchingNodes(treeChanges.exiting,      mc.exiting),
         *   retained: _matchingNodes(treeChanges.retained,     mc.retained),
         *   entering: _matchingNodes(treeChanges.entering,     mc.entering),
         * };
         * ```
         */
        RegisteredHook.prototype._getMatchingNodes = function (treeChanges, transition) {
            var _this = this;
            var criteria = extend(this._getDefaultMatchCriteria(), this.matchCriteria);
            var paths = values(this.tranSvc._pluginapi._getPathTypes());
            return paths.reduce(function (mn, pathtype) {
                // STATE scope criteria matches against every node in the path.
                // TRANSITION scope criteria matches against only the last node in the path
                var isStateHook = pathtype.scope === exports.TransitionHookScope.STATE;
                var path = treeChanges[pathtype.name] || [];
                var nodes = isStateHook ? path : [tail(path)];
                mn[pathtype.name] = _this._matchingNodes(nodes, criteria[pathtype.name], transition);
                return mn;
            }, {});
        };
        /**
         * Determines if this hook's [[matchCriteria]] match the given [[TreeChanges]]
         *
         * @returns an IMatchingNodes object, or null. If an IMatchingNodes object is returned, its values
         * are the matching [[PathNode]]s for each [[HookMatchCriterion]] (to, from, exiting, retained, entering)
         */
        RegisteredHook.prototype.matches = function (treeChanges, transition) {
            var matches = this._getMatchingNodes(treeChanges, transition);
            // Check if all the criteria matched the TreeChanges object
            var allMatched = values(matches).every(identity);
            return allMatched ? matches : null;
        };
        RegisteredHook.prototype.deregister = function () {
            this.removeHookFromRegistry(this);
            this._deregistered = true;
        };
        return RegisteredHook;
    }());
    /** Return a registration function of the requested type. */
    function makeEvent(registry, transitionService, eventType) {
        // Create the object which holds the registered transition hooks.
        var _registeredHooks = (registry._registeredHooks = registry._registeredHooks || {});
        var hooks = (_registeredHooks[eventType.name] = []);
        var removeHookFn = removeFrom(hooks);
        // Create hook registration function on the IHookRegistry for the event
        registry[eventType.name] = hookRegistrationFn;
        function hookRegistrationFn(matchObject, callback, options) {
            if (options === void 0) { options = {}; }
            var registeredHook = new RegisteredHook(transitionService, eventType, callback, matchObject, removeHookFn, options);
            hooks.push(registeredHook);
            return registeredHook.deregister.bind(registeredHook);
        }
        return hookRegistrationFn;
    }

    /**
     * This class returns applicable TransitionHooks for a specific Transition instance.
     *
     * Hooks ([[RegisteredHook]]) may be registered globally, e.g., $transitions.onEnter(...), or locally, e.g.
     * myTransition.onEnter(...).  The HookBuilder finds matching RegisteredHooks (where the match criteria is
     * determined by the type of hook)
     *
     * The HookBuilder also converts RegisteredHooks objects to TransitionHook objects, which are used to run a Transition.
     *
     * The HookBuilder constructor is given the $transitions service and a Transition instance.  Thus, a HookBuilder
     * instance may only be used for one specific Transition object. (side note: the _treeChanges accessor is private
     * in the Transition class, so we must also provide the Transition's _treeChanges)
     */
    var HookBuilder = /** @class */ (function () {
        function HookBuilder(transition) {
            this.transition = transition;
        }
        HookBuilder.prototype.buildHooksForPhase = function (phase) {
            var _this = this;
            var $transitions = this.transition.router.transitionService;
            return $transitions._pluginapi
                ._getEvents(phase)
                .map(function (type) { return _this.buildHooks(type); })
                .reduce(unnestR, [])
                .filter(identity);
        };
        /**
         * Returns an array of newly built TransitionHook objects.
         *
         * - Finds all RegisteredHooks registered for the given `hookType` which matched the transition's [[TreeChanges]].
         * - Finds [[PathNode]] (or `PathNode[]`) to use as the TransitionHook context(s)
         * - For each of the [[PathNode]]s, creates a TransitionHook
         *
         * @param hookType the type of the hook registration function, e.g., 'onEnter', 'onFinish'.
         */
        HookBuilder.prototype.buildHooks = function (hookType) {
            var transition = this.transition;
            var treeChanges = transition.treeChanges();
            // Find all the matching registered hooks for a given hook type
            var matchingHooks = this.getMatchingHooks(hookType, treeChanges, transition);
            if (!matchingHooks)
                return [];
            var baseHookOptions = {
                transition: transition,
                current: transition.options().current,
            };
            var makeTransitionHooks = function (hook) {
                // Fetch the Nodes that caused this hook to match.
                var matches = hook.matches(treeChanges, transition);
                // Select the PathNode[] that will be used as TransitionHook context objects
                var matchingNodes = matches[hookType.criteriaMatchPath.name];
                // Return an array of HookTuples
                return matchingNodes.map(function (node) {
                    var _options = extend({
                        bind: hook.bind,
                        traceData: { hookType: hookType.name, context: node },
                    }, baseHookOptions);
                    var state = hookType.criteriaMatchPath.scope === exports.TransitionHookScope.STATE ? node.state.self : null;
                    var transitionHook = new TransitionHook(transition, state, hook, _options);
                    return { hook: hook, node: node, transitionHook: transitionHook };
                });
            };
            return matchingHooks
                .map(makeTransitionHooks)
                .reduce(unnestR, [])
                .sort(tupleSort(hookType.reverseSort))
                .map(function (tuple) { return tuple.transitionHook; });
        };
        /**
         * Finds all RegisteredHooks from:
         * - The Transition object instance hook registry
         * - The TransitionService ($transitions) global hook registry
         *
         * which matched:
         * - the eventType
         * - the matchCriteria (to, from, exiting, retained, entering)
         *
         * @returns an array of matched [[RegisteredHook]]s
         */
        HookBuilder.prototype.getMatchingHooks = function (hookType, treeChanges, transition) {
            var isCreate = hookType.hookPhase === exports.TransitionHookPhase.CREATE;
            // Instance and Global hook registries
            var $transitions = this.transition.router.transitionService;
            var registries = isCreate ? [$transitions] : [this.transition, $transitions];
            return registries
                .map(function (reg) { return reg.getHooks(hookType.name); }) // Get named hooks from registries
                .filter(assertPredicate(isArray, "broken event named: " + hookType.name)) // Sanity check
                .reduce(unnestR, []) // Un-nest RegisteredHook[][] to RegisteredHook[] array
                .filter(function (hook) { return hook.matches(treeChanges, transition); }); // Only those satisfying matchCriteria
        };
        return HookBuilder;
    }());
    /**
     * A factory for a sort function for HookTuples.
     *
     * The sort function first compares the PathNode depth (how deep in the state tree a node is), then compares
     * the EventHook priority.
     *
     * @param reverseDepthSort a boolean, when true, reverses the sort order for the node depth
     * @returns a tuple sort function
     */
    function tupleSort(reverseDepthSort) {
        if (reverseDepthSort === void 0) { reverseDepthSort = false; }
        return function nodeDepthThenPriority(l, r) {
            var factor = reverseDepthSort ? -1 : 1;
            var depthDelta = (l.node.state.path.length - r.node.state.path.length) * factor;
            return depthDelta !== 0 ? depthDelta : r.hook.priority - l.hook.priority;
        };
    }

    /** @internal */
    var stateSelf = prop('self');
    /**
     * Represents a transition between two states.
     *
     * When navigating to a state, we are transitioning **from** the current state **to** the new state.
     *
     * This object contains all contextual information about the to/from states, parameters, resolves.
     * It has information about all states being entered and exited as a result of the transition.
     */
    var Transition = /** @class */ (function () {
        /**
         * Creates a new Transition object.
         *
         * If the target state is not valid, an error is thrown.
         *
         * @internal
         *
         * @param fromPath The path of [[PathNode]]s from which the transition is leaving.  The last node in the `fromPath`
         *        encapsulates the "from state".
         * @param targetState The target state and parameters being transitioned to (also, the transition options)
         * @param router The [[UIRouter]] instance
         * @internal
         */
        function Transition(fromPath, targetState, router) {
            var _this = this;
            /** @internal */
            this._deferred = services.$q.defer();
            /**
             * This promise is resolved or rejected based on the outcome of the Transition.
             *
             * When the transition is successful, the promise is resolved
             * When the transition is unsuccessful, the promise is rejected with the [[Rejection]] or javascript error
             */
            this.promise = this._deferred.promise;
            /** @internal Holds the hook registration functions such as those passed to Transition.onStart() */
            this._registeredHooks = {};
            /** @internal */
            this._hookBuilder = new HookBuilder(this);
            /** Checks if this transition is currently active/running. */
            this.isActive = function () { return _this.router.globals.transition === _this; };
            this.router = router;
            this._targetState = targetState;
            if (!targetState.valid()) {
                throw new Error(targetState.error());
            }
            // current() is assumed to come from targetState.options, but provide a naive implementation otherwise.
            this._options = extend({ current: val(this) }, targetState.options());
            this.$id = router.transitionService._transitionCount++;
            var toPath = PathUtils.buildToPath(fromPath, targetState);
            this._treeChanges = PathUtils.treeChanges(fromPath, toPath, this._options.reloadState);
            this.createTransitionHookRegFns();
            var onCreateHooks = this._hookBuilder.buildHooksForPhase(exports.TransitionHookPhase.CREATE);
            TransitionHook.invokeHooks(onCreateHooks, function () { return null; });
            this.applyViewConfigs(router);
        }
        /** @internal */
        Transition.prototype.onBefore = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        Transition.prototype.onStart = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        Transition.prototype.onExit = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        Transition.prototype.onRetain = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        Transition.prototype.onEnter = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        Transition.prototype.onFinish = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        Transition.prototype.onSuccess = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        Transition.prototype.onError = function (criteria, callback, options) {
            return;
        };
        /** @internal
         * Creates the transition-level hook registration functions
         * (which can then be used to register hooks)
         */
        Transition.prototype.createTransitionHookRegFns = function () {
            var _this = this;
            this.router.transitionService._pluginapi
                ._getEvents()
                .filter(function (type) { return type.hookPhase !== exports.TransitionHookPhase.CREATE; })
                .forEach(function (type) { return makeEvent(_this, _this.router.transitionService, type); });
        };
        /** @internal */
        Transition.prototype.getHooks = function (hookName) {
            return this._registeredHooks[hookName];
        };
        Transition.prototype.applyViewConfigs = function (router) {
            var enteringStates = this._treeChanges.entering.map(function (node) { return node.state; });
            PathUtils.applyViewConfigs(router.transitionService.$view, this._treeChanges.to, enteringStates);
        };
        /**
         * @internal
         * @returns the internal from [State] object
         */
        Transition.prototype.$from = function () {
            return tail(this._treeChanges.from).state;
        };
        /**
         * @internal
         * @returns the internal to [State] object
         */
        Transition.prototype.$to = function () {
            return tail(this._treeChanges.to).state;
        };
        /**
         * Returns the "from state"
         *
         * Returns the state that the transition is coming *from*.
         *
         * @returns The state declaration object for the Transition's ("from state").
         */
        Transition.prototype.from = function () {
            return this.$from().self;
        };
        /**
         * Returns the "to state"
         *
         * Returns the state that the transition is going *to*.
         *
         * @returns The state declaration object for the Transition's target state ("to state").
         */
        Transition.prototype.to = function () {
            return this.$to().self;
        };
        /**
         * Gets the Target State
         *
         * A transition's [[TargetState]] encapsulates the [[to]] state, the [[params]], and the [[options]] as a single object.
         *
         * @returns the [[TargetState]] of this Transition
         */
        Transition.prototype.targetState = function () {
            return this._targetState;
        };
        /**
         * Determines whether two transitions are equivalent.
         * @deprecated
         */
        Transition.prototype.is = function (compare) {
            if (compare instanceof Transition) {
                // TODO: Also compare parameters
                return this.is({ to: compare.$to().name, from: compare.$from().name });
            }
            return !((compare.to && !matchState(this.$to(), compare.to, this)) ||
                (compare.from && !matchState(this.$from(), compare.from, this)));
        };
        Transition.prototype.params = function (pathname) {
            if (pathname === void 0) { pathname = 'to'; }
            return Object.freeze(this._treeChanges[pathname].map(prop('paramValues')).reduce(mergeR, {}));
        };
        Transition.prototype.paramsChanged = function () {
            var fromParams = this.params('from');
            var toParams = this.params('to');
            // All the parameters declared on both the "to" and "from" paths
            var allParamDescriptors = []
                .concat(this._treeChanges.to)
                .concat(this._treeChanges.from)
                .map(function (pathNode) { return pathNode.paramSchema; })
                .reduce(flattenR, [])
                .reduce(uniqR, []);
            var changedParamDescriptors = Param.changed(allParamDescriptors, fromParams, toParams);
            return changedParamDescriptors.reduce(function (changedValues, descriptor) {
                changedValues[descriptor.id] = toParams[descriptor.id];
                return changedValues;
            }, {});
        };
        /**
         * Creates a [[UIInjector]] Dependency Injector
         *
         * Returns a Dependency Injector for the Transition's target state (to state).
         * The injector provides resolve values which the target state has access to.
         *
         * The `UIInjector` can also provide values from the native root/global injector (ng1/ng2).
         *
         * #### Example:
         * ```js
         * .onEnter({ entering: 'myState' }, trans => {
         *   var myResolveValue = trans.injector().get('myResolve');
         *   // Inject a global service from the global/native injector (if it exists)
         *   var MyService = trans.injector().get('MyService');
         * })
         * ```
         *
         * In some cases (such as `onBefore`), you may need access to some resolve data but it has not yet been fetched.
         * You can use [[UIInjector.getAsync]] to get a promise for the data.
         * #### Example:
         * ```js
         * .onBefore({}, trans => {
         *   return trans.injector().getAsync('myResolve').then(myResolveValue =>
         *     return myResolveValue !== 'ABORT';
         *   });
         * });
         * ```
         *
         * If a `state` is provided, the injector that is returned will be limited to resolve values that the provided state has access to.
         * This can be useful if both a parent state `foo` and a child state `foo.bar` have both defined a resolve such as `data`.
         * #### Example:
         * ```js
         * .onEnter({ to: 'foo.bar' }, trans => {
         *   // returns result of `foo` state's `myResolve` resolve
         *   // even though `foo.bar` also has a `myResolve` resolve
         *   var fooData = trans.injector('foo').get('myResolve');
         * });
         * ```
         *
         * If you need resolve data from the exiting states, pass `'from'` as `pathName`.
         * The resolve data from the `from` path will be returned.
         * #### Example:
         * ```js
         * .onExit({ exiting: 'foo.bar' }, trans => {
         *   // Gets the resolve value of `myResolve` from the state being exited
         *   var fooData = trans.injector(null, 'from').get('myResolve');
         * });
         * ```
         *
         *
         * @param state Limits the resolves provided to only the resolves the provided state has access to.
         * @param pathName Default: `'to'`: Chooses the path for which to create the injector. Use this to access resolves for `exiting` states.
         *
         * @returns a [[UIInjector]]
         */
        Transition.prototype.injector = function (state, pathName) {
            if (pathName === void 0) { pathName = 'to'; }
            var path = this._treeChanges[pathName];
            if (state)
                path = PathUtils.subPath(path, function (node) { return node.state === state || node.state.name === state; });
            return new ResolveContext(path).injector();
        };
        /**
         * Gets all available resolve tokens (keys)
         *
         * This method can be used in conjunction with [[injector]] to inspect the resolve values
         * available to the Transition.
         *
         * This returns all the tokens defined on [[StateDeclaration.resolve]] blocks, for the states
         * in the Transition's [[TreeChanges.to]] path.
         *
         * #### Example:
         * This example logs all resolve values
         * ```js
         * let tokens = trans.getResolveTokens();
         * tokens.forEach(token => console.log(token + " = " + trans.injector().get(token)));
         * ```
         *
         * #### Example:
         * This example creates promises for each resolve value.
         * This triggers fetches of resolves (if any have not yet been fetched).
         * When all promises have all settled, it logs the resolve values.
         * ```js
         * let tokens = trans.getResolveTokens();
         * let promise = tokens.map(token => trans.injector().getAsync(token));
         * Promise.all(promises).then(values => console.log("Resolved values: " + values));
         * ```
         *
         * Note: Angular 1 users whould use `$q.all()`
         *
         * @param pathname resolve context's path name (e.g., `to` or `from`)
         *
         * @returns an array of resolve tokens (keys)
         */
        Transition.prototype.getResolveTokens = function (pathname) {
            if (pathname === void 0) { pathname = 'to'; }
            return new ResolveContext(this._treeChanges[pathname]).getTokens();
        };
        /**
         * Dynamically adds a new [[Resolvable]] (i.e., [[StateDeclaration.resolve]]) to this transition.
         *
         * Allows a transition hook to dynamically add a Resolvable to this Transition.
         *
         * Use the [[Transition.injector]] to retrieve the resolved data in subsequent hooks ([[UIInjector.get]]).
         *
         * If a `state` argument is provided, the Resolvable is processed when that state is being entered.
         * If no `state` is provided then the root state is used.
         * If the given `state` has already been entered, the Resolvable is processed when any child state is entered.
         * If no child states will be entered, the Resolvable is processed during the `onFinish` phase of the Transition.
         *
         * The `state` argument also scopes the resolved data.
         * The resolved data is available from the injector for that `state` and any children states.
         *
         * #### Example:
         * ```js
         * transitionService.onBefore({}, transition => {
         *   transition.addResolvable({
         *     token: 'myResolve',
         *     deps: ['MyService'],
         *     resolveFn: myService => myService.getData()
         *   });
         * });
         * ```
         *
         * @param resolvable a [[ResolvableLiteral]] object (or a [[Resolvable]])
         * @param state the state in the "to path" which should receive the new resolve (otherwise, the root state)
         */
        Transition.prototype.addResolvable = function (resolvable, state) {
            if (state === void 0) { state = ''; }
            resolvable = is(Resolvable)(resolvable) ? resolvable : new Resolvable(resolvable);
            var stateName = typeof state === 'string' ? state : state.name;
            var topath = this._treeChanges.to;
            var targetNode = find(topath, function (node) { return node.state.name === stateName; });
            var resolveContext = new ResolveContext(topath);
            resolveContext.addResolvables([resolvable], targetNode.state);
        };
        /**
         * Gets the transition from which this transition was redirected.
         *
         * If the current transition is a redirect, this method returns the transition that was redirected.
         *
         * #### Example:
         * ```js
         * let transitionA = $state.go('A').transition
         * transitionA.onStart({}, () => $state.target('B'));
         * $transitions.onSuccess({ to: 'B' }, (trans) => {
         *   trans.to().name === 'B'; // true
         *   trans.redirectedFrom() === transitionA; // true
         * });
         * ```
         *
         * @returns The previous Transition, or null if this Transition is not the result of a redirection
         */
        Transition.prototype.redirectedFrom = function () {
            return this._options.redirectedFrom || null;
        };
        /**
         * Gets the original transition in a redirect chain
         *
         * A transition might belong to a long chain of multiple redirects.
         * This method walks the [[redirectedFrom]] chain back to the original (first) transition in the chain.
         *
         * #### Example:
         * ```js
         * // states
         * registry.register({ name: 'A', redirectTo: 'B' });
         * registry.register({ name: 'B', redirectTo: 'C' });
         * registry.register({ name: 'C', redirectTo: 'D' });
         * registry.register({ name: 'D' });
         *
         * let transitionA = $state.go('A').transition
         *
         * $transitions.onSuccess({ to: 'D' }, (trans) => {
         *   trans.to().name === 'D'; // true
         *   trans.redirectedFrom().to().name === 'C'; // true
         *   trans.originalTransition() === transitionA; // true
         *   trans.originalTransition().to().name === 'A'; // true
         * });
         * ```
         *
         * @returns The original Transition that started a redirect chain
         */
        Transition.prototype.originalTransition = function () {
            var rf = this.redirectedFrom();
            return (rf && rf.originalTransition()) || this;
        };
        /**
         * Get the transition options
         *
         * @returns the options for this Transition.
         */
        Transition.prototype.options = function () {
            return this._options;
        };
        /**
         * Gets the states being entered.
         *
         * @returns an array of states that will be entered during this transition.
         */
        Transition.prototype.entering = function () {
            return map(this._treeChanges.entering, prop('state')).map(stateSelf);
        };
        /**
         * Gets the states being exited.
         *
         * @returns an array of states that will be exited during this transition.
         */
        Transition.prototype.exiting = function () {
            return map(this._treeChanges.exiting, prop('state')).map(stateSelf).reverse();
        };
        /**
         * Gets the states being retained.
         *
         * @returns an array of states that are already entered from a previous Transition, that will not be
         *    exited during this Transition
         */
        Transition.prototype.retained = function () {
            return map(this._treeChanges.retained, prop('state')).map(stateSelf);
        };
        /**
         * Get the [[ViewConfig]]s associated with this Transition
         *
         * Each state can define one or more views (template/controller), which are encapsulated as `ViewConfig` objects.
         * This method fetches the `ViewConfigs` for a given path in the Transition (e.g., "to" or "entering").
         *
         * @param pathname the name of the path to fetch views for:
         *   (`'to'`, `'from'`, `'entering'`, `'exiting'`, `'retained'`)
         * @param state If provided, only returns the `ViewConfig`s for a single state in the path
         *
         * @returns a list of ViewConfig objects for the given path.
         */
        Transition.prototype.views = function (pathname, state) {
            if (pathname === void 0) { pathname = 'entering'; }
            var path = this._treeChanges[pathname];
            path = !state ? path : path.filter(propEq('state', state));
            return path.map(prop('views')).filter(identity).reduce(unnestR, []);
        };
        Transition.prototype.treeChanges = function (pathname) {
            return pathname ? this._treeChanges[pathname] : this._treeChanges;
        };
        /**
         * Creates a new transition that is a redirection of the current one.
         *
         * This transition can be returned from a [[TransitionService]] hook to
         * redirect a transition to a new state and/or set of parameters.
         *
         * @internal
         *
         * @returns Returns a new [[Transition]] instance.
         */
        Transition.prototype.redirect = function (targetState) {
            var redirects = 1, trans = this;
            // tslint:disable-next-line:no-conditional-assignment
            while ((trans = trans.redirectedFrom()) != null) {
                if (++redirects > 20)
                    throw new Error("Too many consecutive Transition redirects (20+)");
            }
            var redirectOpts = { redirectedFrom: this, source: 'redirect' };
            // If the original transition was caused by URL sync, then use { location: 'replace' }
            // on the new transition (unless the target state explicitly specifies location: false).
            // This causes the original url to be replaced with the url for the redirect target
            // so the original url disappears from the browser history.
            if (this.options().source === 'url' && targetState.options().location !== false) {
                redirectOpts.location = 'replace';
            }
            var newOptions = extend({}, this.options(), targetState.options(), redirectOpts);
            targetState = targetState.withOptions(newOptions, true);
            var newTransition = this.router.transitionService.create(this._treeChanges.from, targetState);
            var originalEnteringNodes = this._treeChanges.entering;
            var redirectEnteringNodes = newTransition._treeChanges.entering;
            // --- Re-use resolve data from original transition ---
            // When redirecting from a parent state to a child state where the parent parameter values haven't changed
            // (because of the redirect), the resolves fetched by the original transition are still valid in the
            // redirected transition.
            //
            // This allows you to define a redirect on a parent state which depends on an async resolve value.
            // You can wait for the resolve, then redirect to a child state based on the result.
            // The redirected transition does not have to re-fetch the resolve.
            // ---------------------------------------------------------
            var nodeIsReloading = function (reloadState) { return function (node) {
                return reloadState && node.state.includes[reloadState.name];
            }; };
            // Find any "entering" nodes in the redirect path that match the original path and aren't being reloaded
            var matchingEnteringNodes = PathUtils.matching(redirectEnteringNodes, originalEnteringNodes, PathUtils.nonDynamicParams).filter(not(nodeIsReloading(targetState.options().reloadState)));
            // Use the existing (possibly pre-resolved) resolvables for the matching entering nodes.
            matchingEnteringNodes.forEach(function (node, idx) {
                node.resolvables = originalEnteringNodes[idx].resolvables;
            });
            return newTransition;
        };
        /** @internal If a transition doesn't exit/enter any states, returns any [[Param]] whose value changed */
        Transition.prototype._changedParams = function () {
            var tc = this._treeChanges;
            /** Return undefined if it's not a "dynamic" transition, for the following reasons */
            // If user explicitly wants a reload
            if (this._options.reload)
                return undefined;
            // If any states are exiting or entering
            if (tc.exiting.length || tc.entering.length)
                return undefined;
            // If to/from path lengths differ
            if (tc.to.length !== tc.from.length)
                return undefined;
            // If the to/from paths are different
            var pathsDiffer = arrayTuples(tc.to, tc.from)
                .map(function (tuple) { return tuple[0].state !== tuple[1].state; })
                .reduce(anyTrueR, false);
            if (pathsDiffer)
                return undefined;
            // Find any parameter values that differ
            var nodeSchemas = tc.to.map(function (node) { return node.paramSchema; });
            var _a = [tc.to, tc.from].map(function (path) { return path.map(function (x) { return x.paramValues; }); }), toValues = _a[0], fromValues = _a[1];
            var tuples = arrayTuples(nodeSchemas, toValues, fromValues);
            return tuples.map(function (_a) {
                var schema = _a[0], toVals = _a[1], fromVals = _a[2];
                return Param.changed(schema, toVals, fromVals);
            }).reduce(unnestR, []);
        };
        /**
         * Returns true if the transition is dynamic.
         *
         * A transition is dynamic if no states are entered nor exited, but at least one dynamic parameter has changed.
         *
         * @returns true if the Transition is dynamic
         */
        Transition.prototype.dynamic = function () {
            var changes = this._changedParams();
            return !changes ? false : changes.map(function (x) { return x.dynamic; }).reduce(anyTrueR, false);
        };
        /**
         * Returns true if the transition is ignored.
         *
         * A transition is ignored if no states are entered nor exited, and no parameter values have changed.
         *
         * @returns true if the Transition is ignored.
         */
        Transition.prototype.ignored = function () {
            return !!this._ignoredReason();
        };
        /** @internal */
        Transition.prototype._ignoredReason = function () {
            var pending = this.router.globals.transition;
            var reloadState = this._options.reloadState;
            var same = function (pathA, pathB) {
                if (pathA.length !== pathB.length)
                    return false;
                var matching = PathUtils.matching(pathA, pathB);
                return pathA.length === matching.filter(function (node) { return !reloadState || !node.state.includes[reloadState.name]; }).length;
            };
            var newTC = this.treeChanges();
            var pendTC = pending && pending.treeChanges();
            if (pendTC && same(pendTC.to, newTC.to) && same(pendTC.exiting, newTC.exiting))
                return 'SameAsPending';
            if (newTC.exiting.length === 0 && newTC.entering.length === 0 && same(newTC.from, newTC.to))
                return 'SameAsCurrent';
        };
        /**
         * Runs the transition
         *
         * This method is generally called from the [[StateService.transitionTo]]
         *
         * @internal
         *
         * @returns a promise for a successful transition.
         */
        Transition.prototype.run = function () {
            var _this = this;
            var runAllHooks = TransitionHook.runAllHooks;
            // Gets transition hooks array for the given phase
            var getHooksFor = function (phase) { return _this._hookBuilder.buildHooksForPhase(phase); };
            // When the chain is complete, then resolve or reject the deferred
            var transitionSuccess = function () {
                trace.traceSuccess(_this.$to(), _this);
                _this.success = true;
                _this._deferred.resolve(_this.to());
                runAllHooks(getHooksFor(exports.TransitionHookPhase.SUCCESS));
            };
            var transitionError = function (reason) {
                trace.traceError(reason, _this);
                _this.success = false;
                _this._deferred.reject(reason);
                _this._error = reason;
                runAllHooks(getHooksFor(exports.TransitionHookPhase.ERROR));
            };
            var runTransition = function () {
                // Wait to build the RUN hook chain until the BEFORE hooks are done
                // This allows a BEFORE hook to dynamically add additional RUN hooks via the Transition object.
                var allRunHooks = getHooksFor(exports.TransitionHookPhase.RUN);
                var done = function () { return services.$q.when(undefined); };
                return TransitionHook.invokeHooks(allRunHooks, done);
            };
            var startTransition = function () {
                var globals = _this.router.globals;
                globals.lastStartedTransitionId = _this.$id;
                globals.transition = _this;
                globals.transitionHistory.enqueue(_this);
                trace.traceTransitionStart(_this);
                return services.$q.when(undefined);
            };
            var allBeforeHooks = getHooksFor(exports.TransitionHookPhase.BEFORE);
            TransitionHook.invokeHooks(allBeforeHooks, startTransition)
                .then(runTransition)
                .then(transitionSuccess, transitionError);
            return this.promise;
        };
        /**
         * Checks if the Transition is valid
         *
         * @returns true if the Transition is valid
         */
        Transition.prototype.valid = function () {
            return !this.error() || this.success !== undefined;
        };
        /**
         * Aborts this transition
         *
         * Imperative API to abort a Transition.
         * This only applies to Transitions that are not yet complete.
         */
        Transition.prototype.abort = function () {
            // Do not set flag if the transition is already complete
            if (isUndefined(this.success)) {
                this._aborted = true;
            }
        };
        /**
         * The Transition error reason.
         *
         * If the transition is invalid (and could not be run), returns the reason the transition is invalid.
         * If the transition was valid and ran, but was not successful, returns the reason the transition failed.
         *
         * @returns a transition rejection explaining why the transition is invalid, or the reason the transition failed.
         */
        Transition.prototype.error = function () {
            var state = this.$to();
            if (state.self.abstract) {
                return Rejection.invalid("Cannot transition to abstract state '" + state.name + "'");
            }
            var paramDefs = state.parameters();
            var values = this.params();
            var invalidParams = paramDefs.filter(function (param) { return !param.validates(values[param.id]); });
            if (invalidParams.length) {
                var invalidValues = invalidParams.map(function (param) { return "[" + param.id + ":" + stringify(values[param.id]) + "]"; }).join(', ');
                var detail = "The following parameter values are not valid for state '" + state.name + "': " + invalidValues;
                return Rejection.invalid(detail);
            }
            if (this.success === false)
                return this._error;
        };
        /**
         * A string representation of the Transition
         *
         * @returns A string representation of the Transition
         */
        Transition.prototype.toString = function () {
            var fromStateOrName = this.from();
            var toStateOrName = this.to();
            var avoidEmptyHash = function (params) {
                return params['#'] !== null && params['#'] !== undefined ? params : omit(params, ['#']);
            };
            // (X) means the to state is invalid.
            var id = this.$id, from = isObject(fromStateOrName) ? fromStateOrName.name : fromStateOrName, fromParams = stringify(avoidEmptyHash(this._treeChanges.from.map(prop('paramValues')).reduce(mergeR, {}))), toValid = this.valid() ? '' : '(X) ', to = isObject(toStateOrName) ? toStateOrName.name : toStateOrName, toParams = stringify(avoidEmptyHash(this.params()));
            return "Transition#" + id + "( '" + from + "'" + fromParams + " -> " + toValid + "'" + to + "'" + toParams + " )";
        };
        /** @internal */
        Transition.diToken = Transition;
        return Transition;
    }());

    function quoteRegExp(str, param) {
        var surroundPattern = ['', ''], result = str.replace(/[\\\[\]\^$*+?.()|{}]/g, '\\$&');
        if (!param)
            return result;
        switch (param.squash) {
            case false:
                surroundPattern = ['(', ')' + (param.isOptional ? '?' : '')];
                break;
            case true:
                result = result.replace(/\/$/, '');
                surroundPattern = ['(?:/(', ')|/)?'];
                break;
            default:
                surroundPattern = ["(" + param.squash + "|", ')?'];
                break;
        }
        return result + surroundPattern[0] + param.type.pattern.source + surroundPattern[1];
    }
    var memoizeTo = function (obj, _prop, fn) { return (obj[_prop] = obj[_prop] || fn()); };
    var splitOnSlash = splitOnDelim('/');
    var defaultConfig = {
        state: { params: {} },
        strict: true,
        caseInsensitive: true,
        decodeParams: true,
    };
    /**
     * Matches URLs against patterns.
     *
     * Matches URLs against patterns and extracts named parameters from the path or the search
     * part of the URL.
     *
     * A URL pattern consists of a path pattern, optionally followed by '?' and a list of search (query)
     * parameters. Multiple search parameter names are separated by '&'. Search parameters
     * do not influence whether or not a URL is matched, but their values are passed through into
     * the matched parameters returned by [[UrlMatcher.exec]].
     *
     * - *Path parameters* are defined using curly brace placeholders (`/somepath/{param}`)
     * or colon placeholders (`/somePath/:param`).
     *
     * - *A parameter RegExp* may be defined for a param after a colon
     * (`/somePath/{param:[a-zA-Z0-9]+}`) in a curly brace placeholder.
     * The regexp must match for the url to be matched.
     * Should the regexp itself contain curly braces, they must be in matched pairs or escaped with a backslash.
     *
     * Note: a RegExp parameter will encode its value using either [[ParamTypes.path]] or [[ParamTypes.query]].
     *
     * - *Custom parameter types* may also be specified after a colon (`/somePath/{param:int}`) in curly brace parameters.
     *   See [[UrlMatcherFactory.type]] for more information.
     *
     * - *Catch-all parameters* are defined using an asterisk placeholder (`/somepath/*catchallparam`).
     *   A catch-all * parameter value will contain the remainder of the URL.
     *
     * ---
     *
     * Parameter names may contain only word characters (latin letters, digits, and underscore) and
     * must be unique within the pattern (across both path and search parameters).
     * A path parameter matches any number of characters other than '/'. For catch-all
     * placeholders the path parameter matches any number of characters.
     *
     * Examples:
     *
     * * `'/hello/'` - Matches only if the path is exactly '/hello/'. There is no special treatment for
     *   trailing slashes, and patterns have to match the entire path, not just a prefix.
     * * `'/user/:id'` - Matches '/user/bob' or '/user/1234!!!' or even '/user/' but not '/user' or
     *   '/user/bob/details'. The second path segment will be captured as the parameter 'id'.
     * * `'/user/{id}'` - Same as the previous example, but using curly brace syntax.
     * * `'/user/{id:[^/]*}'` - Same as the previous example.
     * * `'/user/{id:[0-9a-fA-F]{1,8}}'` - Similar to the previous example, but only matches if the id
     *   parameter consists of 1 to 8 hex digits.
     * * `'/files/{path:.*}'` - Matches any URL starting with '/files/' and captures the rest of the
     *   path into the parameter 'path'.
     * * `'/files/*path'` - ditto.
     * * `'/calendar/{start:date}'` - Matches "/calendar/2014-11-12" (because the pattern defined
     *   in the built-in  `date` ParamType matches `2014-11-12`) and provides a Date object in $stateParams.start
     *
     */
    var UrlMatcher = /** @class */ (function () {
        /**
         * @param pattern The pattern to compile into a matcher.
         * @param paramTypes The [[ParamTypes]] registry
         * @param paramFactory A [[ParamFactory]] object
         * @param config  A [[UrlMatcherCompileConfig]] configuration object
         */
        function UrlMatcher(pattern, paramTypes, paramFactory, config) {
            var _this = this;
            /** @internal */
            this._cache = { path: [this] };
            /** @internal */
            this._children = [];
            /** @internal */
            this._params = [];
            /** @internal */
            this._segments = [];
            /** @internal */
            this._compiled = [];
            this.config = config = defaults(config, defaultConfig);
            this.pattern = pattern;
            // Find all placeholders and create a compiled pattern, using either classic or curly syntax:
            //   '*' name
            //   ':' name
            //   '{' name '}'
            //   '{' name ':' regexp '}'
            // The regular expression is somewhat complicated due to the need to allow curly braces
            // inside the regular expression. The placeholder regexp breaks down as follows:
            //    ([:*])([\w\[\]]+)              - classic placeholder ($1 / $2) (search version has - for snake-case)
            //    \{([\w\[\]]+)(?:\:\s*( ... ))?\}  - curly brace placeholder ($3) with optional regexp/type ... ($4) (search version has - for snake-case
            //    (?: ... | ... | ... )+         - the regexp consists of any number of atoms, an atom being either
            //    [^{}\\]+                       - anything other than curly braces or backslash
            //    \\.                            - a backslash escape
            //    \{(?:[^{}\\]+|\\.)*\}          - a matched set of curly braces containing other atoms
            var placeholder = /([:*])([\w\[\]]+)|\{([\w\[\]]+)(?:\:\s*((?:[^{}\\]+|\\.|\{(?:[^{}\\]+|\\.)*\})+))?\}/g;
            var searchPlaceholder = /([:]?)([\w\[\].-]+)|\{([\w\[\].-]+)(?:\:\s*((?:[^{}\\]+|\\.|\{(?:[^{}\\]+|\\.)*\})+))?\}/g;
            var patterns = [];
            var last = 0;
            var matchArray;
            var checkParamErrors = function (id) {
                if (!UrlMatcher.nameValidator.test(id))
                    throw new Error("Invalid parameter name '" + id + "' in pattern '" + pattern + "'");
                if (find(_this._params, propEq('id', id)))
                    throw new Error("Duplicate parameter name '" + id + "' in pattern '" + pattern + "'");
            };
            // Split into static segments separated by path parameter placeholders.
            // The number of segments is always 1 more than the number of parameters.
            var matchDetails = function (m, isSearch) {
                // IE[78] returns '' for unmatched groups instead of null
                var id = m[2] || m[3];
                var regexp = isSearch ? m[4] : m[4] || (m[1] === '*' ? '[\\s\\S]*' : null);
                var makeRegexpType = function (str) {
                    return inherit(paramTypes.type(isSearch ? 'query' : 'path'), {
                        pattern: new RegExp(str, _this.config.caseInsensitive ? 'i' : undefined),
                    });
                };
                return {
                    id: id,
                    regexp: regexp,
                    segment: pattern.substring(last, m.index),
                    type: !regexp ? null : paramTypes.type(regexp) || makeRegexpType(regexp),
                };
            };
            var details;
            var segment;
            // tslint:disable-next-line:no-conditional-assignment
            while ((matchArray = placeholder.exec(pattern))) {
                details = matchDetails(matchArray, false);
                if (details.segment.indexOf('?') >= 0)
                    break; // we're into the search part
                checkParamErrors(details.id);
                this._params.push(paramFactory.fromPath(details.id, details.type, config.state));
                this._segments.push(details.segment);
                patterns.push([details.segment, tail(this._params)]);
                last = placeholder.lastIndex;
            }
            segment = pattern.substring(last);
            // Find any search parameter names and remove them from the last segment
            var i = segment.indexOf('?');
            if (i >= 0) {
                var search = segment.substring(i);
                segment = segment.substring(0, i);
                if (search.length > 0) {
                    last = 0;
                    // tslint:disable-next-line:no-conditional-assignment
                    while ((matchArray = searchPlaceholder.exec(search))) {
                        details = matchDetails(matchArray, true);
                        checkParamErrors(details.id);
                        this._params.push(paramFactory.fromSearch(details.id, details.type, config.state));
                        last = placeholder.lastIndex;
                        // check if ?&
                    }
                }
            }
            this._segments.push(segment);
            this._compiled = patterns.map(function (_pattern) { return quoteRegExp.apply(null, _pattern); }).concat(quoteRegExp(segment));
        }
        /** @internal */
        UrlMatcher.encodeDashes = function (str) {
            // Replace dashes with encoded "\-"
            return encodeURIComponent(str).replace(/-/g, function (c) { return "%5C%" + c.charCodeAt(0).toString(16).toUpperCase(); });
        };
        /** @internal Given a matcher, return an array with the matcher's path segments and path params, in order */
        UrlMatcher.pathSegmentsAndParams = function (matcher) {
            var staticSegments = matcher._segments;
            var pathParams = matcher._params.filter(function (p) { return p.location === exports.DefType.PATH; });
            return arrayTuples(staticSegments, pathParams.concat(undefined))
                .reduce(unnestR, [])
                .filter(function (x) { return x !== '' && isDefined(x); });
        };
        /** @internal Given a matcher, return an array with the matcher's query params */
        UrlMatcher.queryParams = function (matcher) {
            return matcher._params.filter(function (p) { return p.location === exports.DefType.SEARCH; });
        };
        /**
         * Compare two UrlMatchers
         *
         * This comparison function converts a UrlMatcher into static and dynamic path segments.
         * Each static path segment is a static string between a path separator (slash character).
         * Each dynamic segment is a path parameter.
         *
         * The comparison function sorts static segments before dynamic ones.
         */
        UrlMatcher.compare = function (a, b) {
            /**
             * Turn a UrlMatcher and all its parent matchers into an array
             * of slash literals '/', string literals, and Param objects
             *
             * This example matcher matches strings like "/foo/:param/tail":
             * var matcher = $umf.compile("/foo").append($umf.compile("/:param")).append($umf.compile("/")).append($umf.compile("tail"));
             * var result = segments(matcher); // [ '/', 'foo', '/', Param, '/', 'tail' ]
             *
             * Caches the result as `matcher._cache.segments`
             */
            var segments = function (matcher) {
                return (matcher._cache.segments =
                    matcher._cache.segments ||
                        matcher._cache.path
                            .map(UrlMatcher.pathSegmentsAndParams)
                            .reduce(unnestR, [])
                            .reduce(joinNeighborsR, [])
                            .map(function (x) { return (isString(x) ? splitOnSlash(x) : x); })
                            .reduce(unnestR, []));
            };
            /**
             * Gets the sort weight for each segment of a UrlMatcher
             *
             * Caches the result as `matcher._cache.weights`
             */
            var weights = function (matcher) {
                return (matcher._cache.weights =
                    matcher._cache.weights ||
                        segments(matcher).map(function (segment) {
                            // Sort slashes first, then static strings, the Params
                            if (segment === '/')
                                return 1;
                            if (isString(segment))
                                return 2;
                            if (segment instanceof Param)
                                return 3;
                        }));
            };
            /**
             * Pads shorter array in-place (mutates)
             */
            var padArrays = function (l, r, padVal) {
                var len = Math.max(l.length, r.length);
                while (l.length < len)
                    l.push(padVal);
                while (r.length < len)
                    r.push(padVal);
            };
            var weightsA = weights(a), weightsB = weights(b);
            padArrays(weightsA, weightsB, 0);
            var _pairs = arrayTuples(weightsA, weightsB);
            var cmp, i;
            for (i = 0; i < _pairs.length; i++) {
                cmp = _pairs[i][0] - _pairs[i][1];
                if (cmp !== 0)
                    return cmp;
            }
            return 0;
        };
        /**
         * Creates a new concatenated UrlMatcher
         *
         * Builds a new UrlMatcher by appending another UrlMatcher to this one.
         *
         * @param url A `UrlMatcher` instance to append as a child of the current `UrlMatcher`.
         */
        UrlMatcher.prototype.append = function (url) {
            this._children.push(url);
            url._cache = {
                path: this._cache.path.concat(url),
                parent: this,
                pattern: null,
            };
            return url;
        };
        /** @internal */
        UrlMatcher.prototype.isRoot = function () {
            return this._cache.path[0] === this;
        };
        /** Returns the input pattern string */
        UrlMatcher.prototype.toString = function () {
            return this.pattern;
        };
        UrlMatcher.prototype._getDecodedParamValue = function (value, param) {
            if (isDefined(value)) {
                if (this.config.decodeParams && !param.type.raw) {
                    if (isArray(value)) {
                        value = value.map(function (paramValue) { return decodeURIComponent(paramValue); });
                    }
                    else {
                        value = decodeURIComponent(value);
                    }
                }
                value = param.type.decode(value);
            }
            return param.value(value);
        };
        /**
         * Tests the specified url/path against this matcher.
         *
         * Tests if the given url matches this matcher's pattern, and returns an object containing the captured
         * parameter values.  Returns null if the path does not match.
         *
         * The returned object contains the values
         * of any search parameters that are mentioned in the pattern, but their value may be null if
         * they are not present in `search`. This means that search parameters are always treated
         * as optional.
         *
         * #### Example:
         * ```js
         * new UrlMatcher('/user/{id}?q&r').exec('/user/bob', {
         *   x: '1', q: 'hello'
         * });
         * // returns { id: 'bob', q: 'hello', r: null }
         * ```
         *
         * @param path    The URL path to match, e.g. `$location.path()`.
         * @param search  URL search parameters, e.g. `$location.search()`.
         * @param hash    URL hash e.g. `$location.hash()`.
         * @param options
         *
         * @returns The captured parameter values.
         */
        UrlMatcher.prototype.exec = function (path, search, hash, options) {
            var _this = this;
            if (search === void 0) { search = {}; }
            var match = memoizeTo(this._cache, 'pattern', function () {
                return new RegExp([
                    '^',
                    unnest(_this._cache.path.map(prop('_compiled'))).join(''),
                    _this.config.strict === false ? '/?' : '',
                    '$',
                ].join(''), _this.config.caseInsensitive ? 'i' : undefined);
            }).exec(path);
            if (!match)
                return null;
            // options = defaults(options, { isolate: false });
            var allParams = this.parameters(), pathParams = allParams.filter(function (param) { return !param.isSearch(); }), searchParams = allParams.filter(function (param) { return param.isSearch(); }), nPathSegments = this._cache.path.map(function (urlm) { return urlm._segments.length - 1; }).reduce(function (a, x) { return a + x; }), values = {};
            if (nPathSegments !== match.length - 1)
                throw new Error("Unbalanced capture group in route '" + this.pattern + "'");
            function decodePathArray(paramVal) {
                var reverseString = function (str) { return str.split('').reverse().join(''); };
                var unquoteDashes = function (str) { return str.replace(/\\-/g, '-'); };
                var split = reverseString(paramVal).split(/-(?!\\)/);
                var allReversed = map(split, reverseString);
                return map(allReversed, unquoteDashes).reverse();
            }
            for (var i = 0; i < nPathSegments; i++) {
                var param = pathParams[i];
                var value = match[i + 1];
                // if the param value matches a pre-replace pair, replace the value before decoding.
                for (var j = 0; j < param.replace.length; j++) {
                    if (param.replace[j].from === value)
                        value = param.replace[j].to;
                }
                if (value && param.array === true)
                    value = decodePathArray(value);
                values[param.id] = this._getDecodedParamValue(value, param);
            }
            searchParams.forEach(function (param) {
                var value = search[param.id];
                for (var j = 0; j < param.replace.length; j++) {
                    if (param.replace[j].from === value)
                        value = param.replace[j].to;
                }
                values[param.id] = _this._getDecodedParamValue(value, param);
            });
            if (hash)
                values['#'] = hash;
            return values;
        };
        /**
         * @internal
         * Returns all the [[Param]] objects of all path and search parameters of this pattern in order of appearance.
         *
         * @returns {Array.<Param>}  An array of [[Param]] objects. Must be treated as read-only. If the
         *    pattern has no parameters, an empty array is returned.
         */
        UrlMatcher.prototype.parameters = function (opts) {
            if (opts === void 0) { opts = {}; }
            if (opts.inherit === false)
                return this._params;
            return unnest(this._cache.path.map(function (matcher) { return matcher._params; }));
        };
        /**
         * @internal
         * Returns a single parameter from this UrlMatcher by id
         *
         * @param id
         * @param opts
         * @returns {T|Param|any|boolean|UrlMatcher|null}
         */
        UrlMatcher.prototype.parameter = function (id, opts) {
            var _this = this;
            if (opts === void 0) { opts = {}; }
            var findParam = function () {
                for (var _i = 0, _a = _this._params; _i < _a.length; _i++) {
                    var param = _a[_i];
                    if (param.id === id)
                        return param;
                }
            };
            var parent = this._cache.parent;
            return findParam() || (opts.inherit !== false && parent && parent.parameter(id, opts)) || null;
        };
        /**
         * Validates the input parameter values against this UrlMatcher
         *
         * Checks an object hash of parameters to validate their correctness according to the parameter
         * types of this `UrlMatcher`.
         *
         * @param params The object hash of parameters to validate.
         * @returns Returns `true` if `params` validates, otherwise `false`.
         */
        UrlMatcher.prototype.validates = function (params) {
            var validParamVal = function (param, val) { return !param || param.validates(val); };
            params = params || {};
            // I'm not sure why this checks only the param keys passed in, and not all the params known to the matcher
            var paramSchema = this.parameters().filter(function (paramDef) { return params.hasOwnProperty(paramDef.id); });
            return paramSchema.map(function (paramDef) { return validParamVal(paramDef, params[paramDef.id]); }).reduce(allTrueR, true);
        };
        /**
         * Given a set of parameter values, creates a URL from this UrlMatcher.
         *
         * Creates a URL that matches this pattern by substituting the specified values
         * for the path and search parameters.
         *
         * #### Example:
         * ```js
         * new UrlMatcher('/user/{id}?q').format({ id:'bob', q:'yes' });
         * // returns '/user/bob?q=yes'
         * ```
         *
         * @param values  the values to substitute for the parameters in this pattern.
         * @returns the formatted URL (path and optionally search part).
         */
        UrlMatcher.prototype.format = function (values) {
            if (values === void 0) { values = {}; }
            // Build the full path of UrlMatchers (including all parent UrlMatchers)
            var urlMatchers = this._cache.path;
            // Extract all the static segments and Params (processed as ParamDetails)
            // into an ordered array
            var pathSegmentsAndParams = urlMatchers
                .map(UrlMatcher.pathSegmentsAndParams)
                .reduce(unnestR, [])
                .map(function (x) { return (isString(x) ? x : getDetails(x)); });
            // Extract the query params into a separate array
            var queryParams = urlMatchers
                .map(UrlMatcher.queryParams)
                .reduce(unnestR, [])
                .map(getDetails);
            var isInvalid = function (param) { return param.isValid === false; };
            if (pathSegmentsAndParams.concat(queryParams).filter(isInvalid).length) {
                return null;
            }
            /**
             * Given a Param, applies the parameter value, then returns detailed information about it
             */
            function getDetails(param) {
                // Normalize to typed value
                var value = param.value(values[param.id]);
                var isValid = param.validates(value);
                var isDefaultValue = param.isDefaultValue(value);
                // Check if we're in squash mode for the parameter
                var squash = isDefaultValue ? param.squash : false;
                // Allow the Parameter's Type to encode the value
                var encoded = param.type.encode(value);
                return { param: param, value: value, isValid: isValid, isDefaultValue: isDefaultValue, squash: squash, encoded: encoded };
            }
            // Build up the path-portion from the list of static segments and parameters
            var pathString = pathSegmentsAndParams.reduce(function (acc, x) {
                // The element is a static segment (a raw string); just append it
                if (isString(x))
                    return acc + x;
                // Otherwise, it's a ParamDetails.
                var squash = x.squash, encoded = x.encoded, param = x.param;
                // If squash is === true, try to remove a slash from the path
                if (squash === true)
                    return acc.match(/\/$/) ? acc.slice(0, -1) : acc;
                // If squash is a string, use the string for the param value
                if (isString(squash))
                    return acc + squash;
                if (squash !== false)
                    return acc; // ?
                if (encoded == null)
                    return acc;
                // If this parameter value is an array, encode the value using encodeDashes
                if (isArray(encoded))
                    return acc + map(encoded, UrlMatcher.encodeDashes).join('-');
                // If the parameter type is "raw", then do not encodeURIComponent
                if (param.raw)
                    return acc + encoded;
                // Encode the value
                return acc + encodeURIComponent(encoded);
            }, '');
            // Build the query string by applying parameter values (array or regular)
            // then mapping to key=value, then flattening and joining using "&"
            var queryString = queryParams
                .map(function (paramDetails) {
                var param = paramDetails.param, squash = paramDetails.squash, encoded = paramDetails.encoded, isDefaultValue = paramDetails.isDefaultValue;
                if (encoded == null || (isDefaultValue && squash !== false))
                    return;
                if (!isArray(encoded))
                    encoded = [encoded];
                if (encoded.length === 0)
                    return;
                if (!param.raw)
                    encoded = map(encoded, encodeURIComponent);
                return encoded.map(function (val) { return param.id + "=" + val; });
            })
                .filter(identity)
                .reduce(unnestR, [])
                .join('&');
            // Concat the pathstring with the queryString (if exists) and the hashString (if exists)
            return pathString + (queryString ? "?" + queryString : '') + (values['#'] ? '#' + values['#'] : '');
        };
        /** @internal */
        UrlMatcher.nameValidator = /^\w+([-.]+\w+)*(?:\[\])?$/;
        return UrlMatcher;
    }());

    var __assign = (undefined && undefined.__assign) || function () {
        __assign = Object.assign || function(t) {
            for (var s, i = 1, n = arguments.length; i < n; i++) {
                s = arguments[i];
                for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                    t[p] = s[p];
            }
            return t;
        };
        return __assign.apply(this, arguments);
    };
    var ParamFactory = /** @class */ (function () {
        function ParamFactory(router) {
            this.router = router;
        }
        ParamFactory.prototype.fromConfig = function (id, type, state) {
            return new Param(id, type, exports.DefType.CONFIG, this.router.urlService.config, state);
        };
        ParamFactory.prototype.fromPath = function (id, type, state) {
            return new Param(id, type, exports.DefType.PATH, this.router.urlService.config, state);
        };
        ParamFactory.prototype.fromSearch = function (id, type, state) {
            return new Param(id, type, exports.DefType.SEARCH, this.router.urlService.config, state);
        };
        return ParamFactory;
    }());
    /**
     * Factory for [[UrlMatcher]] instances.
     *
     * The factory is available to ng1 services as
     * `$urlMatcherFactory` or ng1 providers as `$urlMatcherFactoryProvider`.
     */
    var UrlMatcherFactory = /** @class */ (function () {
        // TODO: move implementations to UrlConfig (urlService.config)
        function UrlMatcherFactory(/** @internal */ router) {
            var _this = this;
            this.router = router;
            /** Creates a new [[Param]] for a given location (DefType) */
            this.paramFactory = new ParamFactory(this.router);
            // TODO: Check if removal of this will break anything, then remove these
            this.UrlMatcher = UrlMatcher;
            this.Param = Param;
            /** @deprecated use [[UrlConfig.caseInsensitive]] */
            this.caseInsensitive = function (value) { return _this.router.urlService.config.caseInsensitive(value); };
            /** @deprecated use [[UrlConfig.defaultSquashPolicy]] */
            this.defaultSquashPolicy = function (value) { return _this.router.urlService.config.defaultSquashPolicy(value); };
            /** @deprecated use [[UrlConfig.strictMode]] */
            this.strictMode = function (value) { return _this.router.urlService.config.strictMode(value); };
            /** @deprecated use [[UrlConfig.type]] */
            this.type = function (name, definition, definitionFn) {
                return _this.router.urlService.config.type(name, definition, definitionFn) || _this;
            };
        }
        /**
         * Creates a [[UrlMatcher]] for the specified pattern.
         *
         * @param pattern  The URL pattern.
         * @param config  The config object hash.
         * @returns The UrlMatcher.
         */
        UrlMatcherFactory.prototype.compile = function (pattern, config) {
            var urlConfig = this.router.urlService.config;
            // backward-compatible support for config.params -> config.state.params
            var params = config && !config.state && config.params;
            config = params ? __assign({ state: { params: params } }, config) : config;
            var globalConfig = {
                strict: urlConfig._isStrictMode,
                caseInsensitive: urlConfig._isCaseInsensitive,
                decodeParams: urlConfig._decodeParams,
            };
            return new UrlMatcher(pattern, urlConfig.paramTypes, this.paramFactory, extend(globalConfig, config));
        };
        /**
         * Returns true if the specified object is a [[UrlMatcher]], or false otherwise.
         *
         * @param object  The object to perform the type check against.
         * @returns `true` if the object matches the `UrlMatcher` interface, by
         *          implementing all the same methods.
         */
        UrlMatcherFactory.prototype.isMatcher = function (object) {
            // TODO: typeof?
            if (!isObject(object))
                return false;
            var result = true;
            forEach(UrlMatcher.prototype, function (val, name) {
                if (isFunction(val))
                    result = result && isDefined(object[name]) && isFunction(object[name]);
            });
            return result;
        };
        /** @internal */
        UrlMatcherFactory.prototype.$get = function () {
            var urlConfig = this.router.urlService.config;
            urlConfig.paramTypes.enqueue = false;
            urlConfig.paramTypes._flushTypeQueue();
            return this;
        };
        return UrlMatcherFactory;
    }());

    /**
     * Creates a [[UrlRule]]
     *
     * Creates a [[UrlRule]] from a:
     *
     * - `string`
     * - [[UrlMatcher]]
     * - `RegExp`
     * - [[StateObject]]
     */
    var UrlRuleFactory = /** @class */ (function () {
        function UrlRuleFactory(router) {
            this.router = router;
        }
        UrlRuleFactory.prototype.compile = function (str) {
            return this.router.urlMatcherFactory.compile(str);
        };
        UrlRuleFactory.prototype.create = function (what, handler) {
            var _this = this;
            var isState = StateObject.isState, isStateDeclaration = StateObject.isStateDeclaration;
            var makeRule = pattern([
                [isString, function (_what) { return makeRule(_this.compile(_what)); }],
                [is(UrlMatcher), function (_what) { return _this.fromUrlMatcher(_what, handler); }],
                [or(isState, isStateDeclaration), function (_what) { return _this.fromState(_what, _this.router); }],
                [is(RegExp), function (_what) { return _this.fromRegExp(_what, handler); }],
                [isFunction, function (_what) { return new BaseUrlRule(_what, handler); }],
            ]);
            var rule = makeRule(what);
            if (!rule)
                throw new Error("invalid 'what' in when()");
            return rule;
        };
        /**
         * A UrlRule which matches based on a UrlMatcher
         *
         * The `handler` may be either a `string`, a [[UrlRuleHandlerFn]] or another [[UrlMatcher]]
         *
         * ## Handler as a function
         *
         * If `handler` is a function, the function is invoked with:
         *
         * - matched parameter values ([[RawParams]] from [[UrlMatcher.exec]])
         * - url: the current Url ([[UrlParts]])
         * - router: the router object ([[UIRouter]])
         *
         * #### Example:
         * ```js
         * var urlMatcher = $umf.compile("/foo/:fooId/:barId");
         * var rule = factory.fromUrlMatcher(urlMatcher, match => "/home/" + match.fooId + "/" + match.barId);
         * var match = rule.match('/foo/123/456'); // results in { fooId: '123', barId: '456' }
         * var result = rule.handler(match); // '/home/123/456'
         * ```
         *
         * ## Handler as UrlMatcher
         *
         * If `handler` is a UrlMatcher, the handler matcher is used to create the new url.
         * The `handler` UrlMatcher is formatted using the matched param from the first matcher.
         * The url is replaced with the result.
         *
         * #### Example:
         * ```js
         * var urlMatcher = $umf.compile("/foo/:fooId/:barId");
         * var handler = $umf.compile("/home/:fooId/:barId");
         * var rule = factory.fromUrlMatcher(urlMatcher, handler);
         * var match = rule.match('/foo/123/456'); // results in { fooId: '123', barId: '456' }
         * var result = rule.handler(match); // '/home/123/456'
         * ```
         */
        UrlRuleFactory.prototype.fromUrlMatcher = function (urlMatcher, handler) {
            var _handler = handler;
            if (isString(handler))
                handler = this.router.urlMatcherFactory.compile(handler);
            if (is(UrlMatcher)(handler))
                _handler = function (match) { return handler.format(match); };
            function matchUrlParamters(url) {
                var params = urlMatcher.exec(url.path, url.search, url.hash);
                return urlMatcher.validates(params) && params;
            }
            // Prioritize URLs, lowest to highest:
            // - Some optional URL parameters, but none matched
            // - No optional parameters in URL
            // - Some optional parameters, some matched
            // - Some optional parameters, all matched
            function matchPriority(params) {
                var optional = urlMatcher.parameters().filter(function (param) { return param.isOptional; });
                if (!optional.length)
                    return 0.000001;
                var matched = optional.filter(function (param) { return params[param.id]; });
                return matched.length / optional.length;
            }
            var details = { urlMatcher: urlMatcher, matchPriority: matchPriority, type: 'URLMATCHER' };
            return extend(new BaseUrlRule(matchUrlParamters, _handler), details);
        };
        /**
         * A UrlRule which matches a state by its url
         *
         * #### Example:
         * ```js
         * var rule = factory.fromState($state.get('foo'), router);
         * var match = rule.match('/foo/123/456'); // results in { fooId: '123', barId: '456' }
         * var result = rule.handler(match);
         * // Starts a transition to 'foo' with params: { fooId: '123', barId: '456' }
         * ```
         */
        UrlRuleFactory.prototype.fromState = function (stateOrDecl, router) {
            var state = StateObject.isStateDeclaration(stateOrDecl) ? stateOrDecl.$$state() : stateOrDecl;
            /**
             * Handles match by transitioning to matched state
             *
             * First checks if the router should start a new transition.
             * A new transition is not required if the current state's URL
             * and the new URL are already identical
             */
            var handler = function (match) {
                var $state = router.stateService;
                var globals = router.globals;
                if ($state.href(state, match) !== $state.href(globals.current, globals.params)) {
                    $state.transitionTo(state, match, { inherit: true, source: 'url' });
                }
            };
            var details = { state: state, type: 'STATE' };
            return extend(this.fromUrlMatcher(state.url, handler), details);
        };
        /**
         * A UrlRule which matches based on a regular expression
         *
         * The `handler` may be either a [[UrlRuleHandlerFn]] or a string.
         *
         * ## Handler as a function
         *
         * If `handler` is a function, the function is invoked with:
         *
         * - regexp match array (from `regexp`)
         * - url: the current Url ([[UrlParts]])
         * - router: the router object ([[UIRouter]])
         *
         * #### Example:
         * ```js
         * var rule = factory.fromRegExp(/^\/foo\/(bar|baz)$/, match => "/home/" + match[1])
         * var match = rule.match('/foo/bar'); // results in [ '/foo/bar', 'bar' ]
         * var result = rule.handler(match); // '/home/bar'
         * ```
         *
         * ## Handler as string
         *
         * If `handler` is a string, the url is *replaced by the string* when the Rule is invoked.
         * The string is first interpolated using `string.replace()` style pattern.
         *
         * #### Example:
         * ```js
         * var rule = factory.fromRegExp(/^\/foo\/(bar|baz)$/, "/home/$1")
         * var match = rule.match('/foo/bar'); // results in [ '/foo/bar', 'bar' ]
         * var result = rule.handler(match); // '/home/bar'
         * ```
         */
        UrlRuleFactory.prototype.fromRegExp = function (regexp, handler) {
            if (regexp.global || regexp.sticky)
                throw new Error('Rule RegExp must not be global or sticky');
            /**
             * If handler is a string, the url will be replaced by the string.
             * If the string has any String.replace() style variables in it (like `$2`),
             * they will be replaced by the captures from [[match]]
             */
            var redirectUrlTo = function (match) {
                // Interpolates matched values into $1 $2, etc using a String.replace()-style pattern
                return handler.replace(/\$(\$|\d{1,2})/, function (m, what) { return match[what === '$' ? 0 : Number(what)]; });
            };
            var _handler = isString(handler) ? redirectUrlTo : handler;
            var matchParamsFromRegexp = function (url) { return regexp.exec(url.path); };
            var details = { regexp: regexp, type: 'REGEXP' };
            return extend(new BaseUrlRule(matchParamsFromRegexp, _handler), details);
        };
        UrlRuleFactory.isUrlRule = function (obj) { return obj && ['type', 'match', 'handler'].every(function (key) { return isDefined(obj[key]); }); };
        return UrlRuleFactory;
    }());
    /**
     * A base rule which calls `match`
     *
     * The value from the `match` function is passed through to the `handler`.
     * @internal
     */
    var BaseUrlRule = /** @class */ (function () {
        function BaseUrlRule(match, handler) {
            var _this = this;
            this.match = match;
            this.type = 'RAW';
            this.matchPriority = function (match) { return 0 - _this.$id; };
            this.handler = handler || identity;
        }
        return BaseUrlRule;
    }());

    function appendBasePath(url, isHtml5, absolute, baseHref) {
        if (baseHref === '/')
            return url;
        if (isHtml5)
            return stripLastPathElement(baseHref) + url;
        if (absolute)
            return baseHref.slice(1) + url;
        return url;
    }
    /**
     * Updates URL and responds to URL changes
     *
     * ### Deprecation warning:
     * This class is now considered to be an internal API
     * Use the [[UrlService]] instead.
     * For configuring URL rules, use the [[UrlRules]] which can be found as [[UrlService.rules]].
     */
    var UrlRouter = /** @class */ (function () {
        /** @internal */
        function UrlRouter(/** @internal */ router) {
            var _this = this;
            this.router = router;
            // Delegate these calls to [[UrlService]]
            /** @deprecated use [[UrlService.sync]]*/
            this.sync = function (evt) { return _this.router.urlService.sync(evt); };
            /** @deprecated use [[UrlService.listen]]*/
            this.listen = function (enabled) { return _this.router.urlService.listen(enabled); };
            /** @deprecated use [[UrlService.deferIntercept]]*/
            this.deferIntercept = function (defer) { return _this.router.urlService.deferIntercept(defer); };
            /** @deprecated use [[UrlService.match]]*/
            this.match = function (urlParts) { return _this.router.urlService.match(urlParts); };
            // Delegate these calls to [[UrlRules]]
            /** @deprecated use [[UrlRules.initial]]*/
            this.initial = function (handler) {
                return _this.router.urlService.rules.initial(handler);
            };
            /** @deprecated use [[UrlRules.otherwise]]*/
            this.otherwise = function (handler) {
                return _this.router.urlService.rules.otherwise(handler);
            };
            /** @deprecated use [[UrlRules.removeRule]]*/
            this.removeRule = function (rule) { return _this.router.urlService.rules.removeRule(rule); };
            /** @deprecated use [[UrlRules.rule]]*/
            this.rule = function (rule) { return _this.router.urlService.rules.rule(rule); };
            /** @deprecated use [[UrlRules.rules]]*/
            this.rules = function () { return _this.router.urlService.rules.rules(); };
            /** @deprecated use [[UrlRules.sort]]*/
            this.sort = function (compareFn) { return _this.router.urlService.rules.sort(compareFn); };
            /** @deprecated use [[UrlRules.when]]*/
            this.when = function (matcher, handler, options) { return _this.router.urlService.rules.when(matcher, handler, options); };
            this.urlRuleFactory = new UrlRuleFactory(router);
        }
        /** Internal API. */
        UrlRouter.prototype.update = function (read) {
            var $url = this.router.locationService;
            if (read) {
                this.location = $url.url();
                return;
            }
            if ($url.url() === this.location)
                return;
            $url.url(this.location, true);
        };
        /**
         * Internal API.
         *
         * Pushes a new location to the browser history.
         *
         * @internal
         * @param urlMatcher
         * @param params
         * @param options
         */
        UrlRouter.prototype.push = function (urlMatcher, params, options) {
            var replace = options && !!options.replace;
            this.router.urlService.url(urlMatcher.format(params || {}), replace);
        };
        /**
         * Builds and returns a URL with interpolated parameters
         *
         * #### Example:
         * ```js
         * matcher = $umf.compile("/about/:person");
         * params = { person: "bob" };
         * $bob = $urlRouter.href(matcher, params);
         * // $bob == "/about/bob";
         * ```
         *
         * @param urlMatcher The [[UrlMatcher]] object which is used as the template of the URL to generate.
         * @param params An object of parameter values to fill the matcher's required parameters.
         * @param options Options object. The options are:
         *
         * - **`absolute`** - {boolean=false},  If true will generate an absolute url, e.g. "http://www.example.com/fullurl".
         *
         * @returns Returns the fully compiled URL, or `null` if `params` fail validation against `urlMatcher`
         */
        UrlRouter.prototype.href = function (urlMatcher, params, options) {
            var url = urlMatcher.format(params);
            if (url == null)
                return null;
            options = options || { absolute: false };
            var cfg = this.router.urlService.config;
            var isHtml5 = cfg.html5Mode();
            if (!isHtml5 && url !== null) {
                url = '#' + cfg.hashPrefix() + url;
            }
            url = appendBasePath(url, isHtml5, options.absolute, cfg.baseHref());
            if (!options.absolute || !url) {
                return url;
            }
            var slash = !isHtml5 && url ? '/' : '';
            var cfgPort = cfg.port();
            var port = (cfgPort === 80 || cfgPort === 443 ? '' : ':' + cfgPort);
            return [cfg.protocol(), '://', cfg.host(), port, slash, url].join('');
        };
        Object.defineProperty(UrlRouter.prototype, "interceptDeferred", {
            /** @deprecated use [[UrlService.interceptDeferred]]*/
            get: function () {
                return this.router.urlService.interceptDeferred;
            },
            enumerable: false,
            configurable: true
        });
        return UrlRouter;
    }());

    /**
     * The View service
     *
     * This service pairs existing `ui-view` components (which live in the DOM)
     * with view configs (from the state declaration objects: [[StateDeclaration.views]]).
     *
     * - After a successful Transition, the views from the newly entered states are activated via [[activateViewConfig]].
     *   The views from exited states are deactivated via [[deactivateViewConfig]].
     *   (See: the [[registerActivateViews]] Transition Hook)
     *
     * - As `ui-view` components pop in and out of existence, they register themselves using [[registerUIView]].
     *
     * - When the [[sync]] function is called, the registered `ui-view`(s) ([[ActiveUIView]])
     * are configured with the matching [[ViewConfig]](s)
     *
     */
    var ViewService = /** @class */ (function () {
        /** @internal */
        function ViewService(/** @internal */ router) {
            var _this = this;
            this.router = router;
            /** @internal */ this._uiViews = [];
            /** @internal */ this._viewConfigs = [];
            /** @internal */ this._viewConfigFactories = {};
            /** @internal */ this._listeners = [];
            /** @internal */
            this._pluginapi = {
                _rootViewContext: this._rootViewContext.bind(this),
                _viewConfigFactory: this._viewConfigFactory.bind(this),
                _registeredUIView: function (id) { return find(_this._uiViews, function (view) { return _this.router.$id + "." + view.id === id; }); },
                _registeredUIViews: function () { return _this._uiViews; },
                _activeViewConfigs: function () { return _this._viewConfigs; },
                _onSync: function (listener) {
                    _this._listeners.push(listener);
                    return function () { return removeFrom(_this._listeners, listener); };
                },
            };
        }
        /**
         * Normalizes a view's name from a state.views configuration block.
         *
         * This should be used by a framework implementation to calculate the values for
         * [[_ViewDeclaration.$uiViewName]] and [[_ViewDeclaration.$uiViewContextAnchor]].
         *
         * @param context the context object (state declaration) that the view belongs to
         * @param rawViewName the name of the view, as declared in the [[StateDeclaration.views]]
         *
         * @returns the normalized uiViewName and uiViewContextAnchor that the view targets
         */
        ViewService.normalizeUIViewTarget = function (context, rawViewName) {
            if (rawViewName === void 0) { rawViewName = ''; }
            // TODO: Validate incoming view name with a regexp to allow:
            // ex: "view.name@foo.bar" , "^.^.view.name" , "view.name@^.^" , "" ,
            // "@" , "$default@^" , "!$default.$default" , "!foo.bar"
            var viewAtContext = rawViewName.split('@');
            var uiViewName = viewAtContext[0] || '$default'; // default to unnamed view
            var uiViewContextAnchor = isString(viewAtContext[1]) ? viewAtContext[1] : '^'; // default to parent context
            // Handle relative view-name sugar syntax.
            // Matches rawViewName "^.^.^.foo.bar" into array: ["^.^.^.foo.bar", "^.^.^", "foo.bar"],
            var relativeViewNameSugar = /^(\^(?:\.\^)*)\.(.*$)/.exec(uiViewName);
            if (relativeViewNameSugar) {
                // Clobbers existing contextAnchor (rawViewName validation will fix this)
                uiViewContextAnchor = relativeViewNameSugar[1]; // set anchor to "^.^.^"
                uiViewName = relativeViewNameSugar[2]; // set view-name to "foo.bar"
            }
            if (uiViewName.charAt(0) === '!') {
                uiViewName = uiViewName.substr(1);
                uiViewContextAnchor = ''; // target absolutely from root
            }
            // handle parent relative targeting "^.^.^"
            var relativeMatch = /^(\^(?:\.\^)*)$/;
            if (relativeMatch.exec(uiViewContextAnchor)) {
                var anchorState = uiViewContextAnchor.split('.').reduce(function (anchor, x) { return anchor.parent; }, context);
                uiViewContextAnchor = anchorState.name;
            }
            else if (uiViewContextAnchor === '.') {
                uiViewContextAnchor = context.name;
            }
            return { uiViewName: uiViewName, uiViewContextAnchor: uiViewContextAnchor };
        };
        /** @internal */
        ViewService.prototype._rootViewContext = function (context) {
            return (this._rootContext = context || this._rootContext);
        };
        /** @internal */
        ViewService.prototype._viewConfigFactory = function (viewType, factory) {
            this._viewConfigFactories[viewType] = factory;
        };
        ViewService.prototype.createViewConfig = function (path, decl) {
            var cfgFactory = this._viewConfigFactories[decl.$type];
            if (!cfgFactory)
                throw new Error('ViewService: No view config factory registered for type ' + decl.$type);
            var cfgs = cfgFactory(path, decl);
            return isArray(cfgs) ? cfgs : [cfgs];
        };
        /**
         * Deactivates a ViewConfig.
         *
         * This function deactivates a `ViewConfig`.
         * After calling [[sync]], it will un-pair from any `ui-view` with which it is currently paired.
         *
         * @param viewConfig The ViewConfig view to deregister.
         */
        ViewService.prototype.deactivateViewConfig = function (viewConfig) {
            trace.traceViewServiceEvent('<- Removing', viewConfig);
            removeFrom(this._viewConfigs, viewConfig);
        };
        ViewService.prototype.activateViewConfig = function (viewConfig) {
            trace.traceViewServiceEvent('-> Registering', viewConfig);
            this._viewConfigs.push(viewConfig);
        };
        ViewService.prototype.sync = function () {
            var _this = this;
            var uiViewsByFqn = this._uiViews.map(function (uiv) { return [uiv.fqn, uiv]; }).reduce(applyPairs, {});
            // Return a weighted depth value for a uiView.
            // The depth is the nesting depth of ui-views (based on FQN; times 10,000)
            // plus the depth of the state that is populating the uiView
            function uiViewDepth(uiView) {
                var stateDepth = function (context) { return (context && context.parent ? stateDepth(context.parent) + 1 : 1); };
                return uiView.fqn.split('.').length * 10000 + stateDepth(uiView.creationContext);
            }
            // Return the ViewConfig's context's depth in the context tree.
            function viewConfigDepth(config) {
                var context = config.viewDecl.$context, count = 0;
                while (++count && context.parent)
                    context = context.parent;
                return count;
            }
            // Given a depth function, returns a compare function which can return either ascending or descending order
            var depthCompare = curry(function (depthFn, posNeg, left, right) { return posNeg * (depthFn(left) - depthFn(right)); });
            var matchingConfigPair = function (uiView) {
                var matchingConfigs = _this._viewConfigs.filter(ViewService.matches(uiViewsByFqn, uiView));
                if (matchingConfigs.length > 1) {
                    // This is OK.  Child states can target a ui-view that the parent state also targets (the child wins)
                    // Sort by depth and return the match from the deepest child
                    // console.log(`Multiple matching view configs for ${uiView.fqn}`, matchingConfigs);
                    matchingConfigs.sort(depthCompare(viewConfigDepth, -1)); // descending
                }
                return { uiView: uiView, viewConfig: matchingConfigs[0] };
            };
            var configureUIView = function (tuple) {
                // If a parent ui-view is reconfigured, it could destroy child ui-views.
                // Before configuring a child ui-view, make sure it's still in the active uiViews array.
                if (_this._uiViews.indexOf(tuple.uiView) !== -1)
                    tuple.uiView.configUpdated(tuple.viewConfig);
            };
            // Sort views by FQN and state depth. Process uiviews nearest the root first.
            var uiViewTuples = this._uiViews.sort(depthCompare(uiViewDepth, 1)).map(matchingConfigPair);
            var matchedViewConfigs = uiViewTuples.map(function (tuple) { return tuple.viewConfig; });
            var unmatchedConfigTuples = this._viewConfigs
                .filter(function (config) { return !inArray(matchedViewConfigs, config); })
                .map(function (viewConfig) { return ({ uiView: undefined, viewConfig: viewConfig }); });
            uiViewTuples.forEach(configureUIView);
            var allTuples = uiViewTuples.concat(unmatchedConfigTuples);
            this._listeners.forEach(function (cb) { return cb(allTuples); });
            trace.traceViewSync(allTuples);
        };
        /**
         * Registers a `ui-view` component
         *
         * When a `ui-view` component is created, it uses this method to register itself.
         * After registration the [[sync]] method is used to ensure all `ui-view` are configured with the proper [[ViewConfig]].
         *
         * Note: the `ui-view` component uses the `ViewConfig` to determine what view should be loaded inside the `ui-view`,
         * and what the view's state context is.
         *
         * Note: There is no corresponding `deregisterUIView`.
         *       A `ui-view` should hang on to the return value of `registerUIView` and invoke it to deregister itself.
         *
         * @param uiView The metadata for a UIView
         * @return a de-registration function used when the view is destroyed.
         */
        ViewService.prototype.registerUIView = function (uiView) {
            trace.traceViewServiceUIViewEvent('-> Registering', uiView);
            var uiViews = this._uiViews;
            var fqnAndTypeMatches = function (uiv) { return uiv.fqn === uiView.fqn && uiv.$type === uiView.$type; };
            if (uiViews.filter(fqnAndTypeMatches).length)
                trace.traceViewServiceUIViewEvent('!!!! duplicate uiView named:', uiView);
            uiViews.push(uiView);
            this.sync();
            return function () {
                var idx = uiViews.indexOf(uiView);
                if (idx === -1) {
                    trace.traceViewServiceUIViewEvent('Tried removing non-registered uiView', uiView);
                    return;
                }
                trace.traceViewServiceUIViewEvent('<- Deregistering', uiView);
                removeFrom(uiViews)(uiView);
            };
        };
        /**
         * Returns the list of views currently available on the page, by fully-qualified name.
         *
         * @return {Array} Returns an array of fully-qualified view names.
         */
        ViewService.prototype.available = function () {
            return this._uiViews.map(prop('fqn'));
        };
        /**
         * Returns the list of views on the page containing loaded content.
         *
         * @return {Array} Returns an array of fully-qualified view names.
         */
        ViewService.prototype.active = function () {
            return this._uiViews.filter(prop('$config')).map(prop('name'));
        };
        /**
         * Given a ui-view and a ViewConfig, determines if they "match".
         *
         * A ui-view has a fully qualified name (fqn) and a context object.  The fqn is built from its overall location in
         * the DOM, describing its nesting relationship to any parent ui-view tags it is nested inside of.
         *
         * A ViewConfig has a target ui-view name and a context anchor.  The ui-view name can be a simple name, or
         * can be a segmented ui-view path, describing a portion of a ui-view fqn.
         *
         * In order for a ui-view to match ViewConfig, ui-view's $type must match the ViewConfig's $type
         *
         * If the ViewConfig's target ui-view name is a simple name (no dots), then a ui-view matches if:
         * - the ui-view's name matches the ViewConfig's target name
         * - the ui-view's context matches the ViewConfig's anchor
         *
         * If the ViewConfig's target ui-view name is a segmented name (with dots), then a ui-view matches if:
         * - There exists a parent ui-view where:
         *    - the parent ui-view's name matches the first segment (index 0) of the ViewConfig's target name
         *    - the parent ui-view's context matches the ViewConfig's anchor
         * - And the remaining segments (index 1..n) of the ViewConfig's target name match the tail of the ui-view's fqn
         *
         * Example:
         *
         * DOM:
         * <ui-view>                        <!-- created in the root context (name: "") -->
         *   <ui-view name="foo">                <!-- created in the context named: "A"      -->
         *     <ui-view>                    <!-- created in the context named: "A.B"    -->
         *       <ui-view name="bar">            <!-- created in the context named: "A.B.C"  -->
         *       </ui-view>
         *     </ui-view>
         *   </ui-view>
         * </ui-view>
         *
         * uiViews: [
         *  { fqn: "$default",                  creationContext: { name: "" } },
         *  { fqn: "$default.foo",              creationContext: { name: "A" } },
         *  { fqn: "$default.foo.$default",     creationContext: { name: "A.B" } }
         *  { fqn: "$default.foo.$default.bar", creationContext: { name: "A.B.C" } }
         * ]
         *
         * These four view configs all match the ui-view with the fqn: "$default.foo.$default.bar":
         *
         * - ViewConfig1: { uiViewName: "bar",                       uiViewContextAnchor: "A.B.C" }
         * - ViewConfig2: { uiViewName: "$default.bar",              uiViewContextAnchor: "A.B" }
         * - ViewConfig3: { uiViewName: "foo.$default.bar",          uiViewContextAnchor: "A" }
         * - ViewConfig4: { uiViewName: "$default.foo.$default.bar", uiViewContextAnchor: "" }
         *
         * Using ViewConfig3 as an example, it matches the ui-view with fqn "$default.foo.$default.bar" because:
         * - The ViewConfig's segmented target name is: [ "foo", "$default", "bar" ]
         * - There exists a parent ui-view (which has fqn: "$default.foo") where:
         *    - the parent ui-view's name "foo" matches the first segment "foo" of the ViewConfig's target name
         *    - the parent ui-view's context "A" matches the ViewConfig's anchor context "A"
         * - And the remaining segments [ "$default", "bar" ].join("."_ of the ViewConfig's target name match
         *   the tail of the ui-view's fqn "default.bar"
         *
         * @internal
         */
        ViewService.matches = function (uiViewsByFqn, uiView) { return function (viewConfig) {
            // Don't supply an ng1 ui-view with an ng2 ViewConfig, etc
            if (uiView.$type !== viewConfig.viewDecl.$type)
                return false;
            // Split names apart from both viewConfig and uiView into segments
            var vc = viewConfig.viewDecl;
            var vcSegments = vc.$uiViewName.split('.');
            var uivSegments = uiView.fqn.split('.');
            // Check if the tails of the segment arrays match. ex, these arrays' tails match:
            // vc: ["foo", "bar"], uiv fqn: ["$default", "foo", "bar"]
            if (!equals(vcSegments, uivSegments.slice(0 - vcSegments.length)))
                return false;
            // Now check if the fqn ending at the first segment of the viewConfig matches the context:
            // ["$default", "foo"].join(".") == "$default.foo", does the ui-view $default.foo context match?
            var negOffset = 1 - vcSegments.length || undefined;
            var fqnToFirstSegment = uivSegments.slice(0, negOffset).join('.');
            var uiViewContext = uiViewsByFqn[fqnToFirstSegment].creationContext;
            return vc.$uiViewContextAnchor === (uiViewContext && uiViewContext.name);
        }; };
        return ViewService;
    }());

    /**
     * Global router state
     *
     * This is where we hold the global mutable state such as current state, current
     * params, current transition, etc.
     */
    var UIRouterGlobals = /** @class */ (function () {
        function UIRouterGlobals() {
            /**
             * Current parameter values
             *
             * The parameter values from the latest successful transition
             */
            this.params = new StateParams();
            /** @internal */
            this.lastStartedTransitionId = -1;
            /** @internal */
            this.transitionHistory = new Queue([], 1);
            /** @internal */
            this.successfulTransitions = new Queue([], 1);
        }
        UIRouterGlobals.prototype.dispose = function () {
            this.transitionHistory.clear();
            this.successfulTransitions.clear();
            this.transition = null;
        };
        return UIRouterGlobals;
    }());

    var prioritySort = function (a, b) { return (b.priority || 0) - (a.priority || 0); };
    var typeSort = function (a, b) {
        var weights = { STATE: 4, URLMATCHER: 4, REGEXP: 3, RAW: 2, OTHER: 1 };
        return (weights[a.type] || 0) - (weights[b.type] || 0);
    };
    var urlMatcherSort = function (a, b) {
        return !a.urlMatcher || !b.urlMatcher ? 0 : UrlMatcher.compare(a.urlMatcher, b.urlMatcher);
    };
    var idSort = function (a, b) {
        // Identically sorted STATE and URLMATCHER best rule will be chosen by `matchPriority` after each rule matches the URL
        var useMatchPriority = { STATE: true, URLMATCHER: true };
        var equal = useMatchPriority[a.type] && useMatchPriority[b.type];
        return equal ? 0 : (a.$id || 0) - (b.$id || 0);
    };
    /**
     * Default rule priority sorting function.
     *
     * Sorts rules by:
     *
     * - Explicit priority (set rule priority using [[UrlRules.when]])
     * - Rule type (STATE: 4, URLMATCHER: 4, REGEXP: 3, RAW: 2, OTHER: 1)
     * - `UrlMatcher` specificity ([[UrlMatcher.compare]]): works for STATE and URLMATCHER types to pick the most specific rule.
     * - Rule registration order (for rule types other than STATE and URLMATCHER)
     *   - Equally sorted State and UrlMatcher rules will each match the URL.
     *     Then, the *best* match is chosen based on how many parameter values were matched.
     */
    var defaultRuleSortFn;
    defaultRuleSortFn = function (a, b) {
        var cmp = prioritySort(a, b);
        if (cmp !== 0)
            return cmp;
        cmp = typeSort(a, b);
        if (cmp !== 0)
            return cmp;
        cmp = urlMatcherSort(a, b);
        if (cmp !== 0)
            return cmp;
        return idSort(a, b);
    };
    function getHandlerFn(handler) {
        if (!isFunction(handler) && !isString(handler) && !is(TargetState)(handler) && !TargetState.isDef(handler)) {
            throw new Error("'handler' must be a string, function, TargetState, or have a state: 'newtarget' property");
        }
        return isFunction(handler) ? handler : val(handler);
    }
    /**
     * API for managing URL rules
     *
     * This API is used to create and manage URL rules.
     * URL rules are a mechanism to respond to specific URL patterns.
     *
     * The most commonly used methods are [[otherwise]] and [[when]].
     *
     * This API is found at `router.urlService.rules` (see: [[UIRouter.urlService]], [[URLService.rules]])
     */
    var UrlRules = /** @class */ (function () {
        /** @internal */
        function UrlRules(/** @internal */ router) {
            this.router = router;
            /** @internal */ this._sortFn = defaultRuleSortFn;
            /** @internal */ this._rules = [];
            /** @internal */ this._id = 0;
            this.urlRuleFactory = new UrlRuleFactory(router);
        }
        /** @internal */
        UrlRules.prototype.dispose = function (router) {
            this._rules = [];
            delete this._otherwiseFn;
        };
        /**
         * Defines the initial state, path, or behavior to use when the app starts.
         *
         * This rule defines the initial/starting state for the application.
         *
         * This rule is triggered the first time the URL is checked (when the app initially loads).
         * The rule is triggered only when the url matches either `""` or `"/"`.
         *
         * Note: The rule is intended to be used when the root of the application is directly linked to.
         * When the URL is *not* `""` or `"/"` and doesn't match other rules, the [[otherwise]] rule is triggered.
         * This allows 404-like behavior when an unknown URL is deep-linked.
         *
         * #### Example:
         * Start app at `home` state.
         * ```js
         * .initial({ state: 'home' });
         * ```
         *
         * #### Example:
         * Start app at `/home` (by url)
         * ```js
         * .initial('/home');
         * ```
         *
         * #### Example:
         * When no other url rule matches, go to `home` state
         * ```js
         * .initial((matchValue, url, router) => {
         *   console.log('initial state');
         *   return { state: 'home' };
         * })
         * ```
         *
         * @param handler The initial state or url path, or a function which returns the state or url path (or performs custom logic).
         */
        UrlRules.prototype.initial = function (handler) {
            var handlerFn = getHandlerFn(handler);
            var matchFn = function (urlParts, router) {
                return router.globals.transitionHistory.size() === 0 && !!/^\/?$/.exec(urlParts.path);
            };
            this.rule(this.urlRuleFactory.create(matchFn, handlerFn));
        };
        /**
         * Defines the state, url, or behavior to use when no other rule matches the URL.
         *
         * This rule is matched when *no other rule* matches.
         * It is generally used to handle unknown URLs (similar to "404" behavior, but on the client side).
         *
         * - If `handler` a string, it is treated as a url redirect
         *
         * #### Example:
         * When no other url rule matches, redirect to `/index`
         * ```js
         * .otherwise('/index');
         * ```
         *
         * - If `handler` is an object with a `state` property, the state is activated.
         *
         * #### Example:
         * When no other url rule matches, redirect to `home` and provide a `dashboard` parameter value.
         * ```js
         * .otherwise({ state: 'home', params: { dashboard: 'default' } });
         * ```
         *
         * - If `handler` is a function, the function receives the current url ([[UrlParts]]) and the [[UIRouter]] object.
         *   The function can perform actions, and/or return a value.
         *
         * #### Example:
         * When no other url rule matches, manually trigger a transition to the `home` state
         * ```js
         * .otherwise((matchValue, urlParts, router) => {
         *   router.stateService.go('home');
         * });
         * ```
         *
         * #### Example:
         * When no other url rule matches, go to `home` state
         * ```js
         * .otherwise((matchValue, urlParts, router) => {
         *   return { state: 'home' };
         * });
         * ```
         *
         * @param handler The url path to redirect to, or a function which returns the url path (or performs custom logic).
         */
        UrlRules.prototype.otherwise = function (handler) {
            var handlerFn = getHandlerFn(handler);
            this._otherwiseFn = this.urlRuleFactory.create(val(true), handlerFn);
            this._sorted = false;
        };
        /**
         * Remove a rule previously registered
         *
         * @param rule the matcher rule that was previously registered using [[rule]]
         */
        UrlRules.prototype.removeRule = function (rule) {
            removeFrom(this._rules, rule);
        };
        /**
         * Manually adds a URL Rule.
         *
         * Usually, a url rule is added using [[StateDeclaration.url]] or [[when]].
         * This api can be used directly for more control (to register a [[BaseUrlRule]], for example).
         * Rules can be created using [[urlRuleFactory]], or created manually as simple objects.
         *
         * A rule should have a `match` function which returns truthy if the rule matched.
         * It should also have a `handler` function which is invoked if the rule is the best match.
         *
         * @return a function that deregisters the rule
         */
        UrlRules.prototype.rule = function (rule) {
            var _this = this;
            if (!UrlRuleFactory.isUrlRule(rule))
                throw new Error('invalid rule');
            rule.$id = this._id++;
            rule.priority = rule.priority || 0;
            this._rules.push(rule);
            this._sorted = false;
            return function () { return _this.removeRule(rule); };
        };
        /**
         * Gets all registered rules
         *
         * @returns an array of all the registered rules
         */
        UrlRules.prototype.rules = function () {
            this.ensureSorted();
            return this._rules.concat(this._otherwiseFn ? [this._otherwiseFn] : []);
        };
        /**
         * Defines URL Rule priorities
         *
         * More than one rule ([[UrlRule]]) might match a given URL.
         * This `compareFn` is used to sort the rules by priority.
         * Higher priority rules should sort earlier.
         *
         * The [[defaultRuleSortFn]] is used by default.
         *
         * You only need to call this function once.
         * The `compareFn` will be used to sort the rules as each is registered.
         *
         * If called without any parameter, it will re-sort the rules.
         *
         * ---
         *
         * Url rules may come from multiple sources: states's urls ([[StateDeclaration.url]]), [[when]], and [[rule]].
         * Each rule has a (user-provided) [[UrlRule.priority]], a [[UrlRule.type]], and a [[UrlRule.$id]]
         * The `$id` is is the order in which the rule was registered.
         *
         * The sort function should use these data, or data found on a specific type
         * of [[UrlRule]] (such as [[StateRule.state]]), to order the rules as desired.
         *
         * #### Example:
         * This compare function prioritizes rules by the order in which the rules were registered.
         * A rule registered earlier has higher priority.
         *
         * ```js
         * function compareFn(a, b) {
         *   return a.$id - b.$id;
         * }
         * ```
         *
         * @param compareFn a function that compares to [[UrlRule]] objects.
         *    The `compareFn` should abide by the `Array.sort` compare function rules.
         *    Given two rules, `a` and `b`, return a negative number if `a` should be higher priority.
         *    Return a positive number if `b` should be higher priority.
         *    Return `0` if the rules are identical.
         *
         *    See the [mozilla reference](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#Description)
         *    for details.
         */
        UrlRules.prototype.sort = function (compareFn) {
            var sorted = this.stableSort(this._rules, (this._sortFn = compareFn || this._sortFn));
            // precompute _sortGroup values and apply to each rule
            var group = 0;
            for (var i = 0; i < sorted.length; i++) {
                sorted[i]._group = group;
                if (i < sorted.length - 1 && this._sortFn(sorted[i], sorted[i + 1]) !== 0) {
                    group++;
                }
            }
            this._rules = sorted;
            this._sorted = true;
        };
        /** @internal */
        UrlRules.prototype.ensureSorted = function () {
            this._sorted || this.sort();
        };
        /** @internal */
        UrlRules.prototype.stableSort = function (arr, compareFn) {
            var arrOfWrapper = arr.map(function (elem, idx) { return ({ elem: elem, idx: idx }); });
            arrOfWrapper.sort(function (wrapperA, wrapperB) {
                var cmpDiff = compareFn(wrapperA.elem, wrapperB.elem);
                return cmpDiff === 0 ? wrapperA.idx - wrapperB.idx : cmpDiff;
            });
            return arrOfWrapper.map(function (wrapper) { return wrapper.elem; });
        };
        /**
         * Registers a `matcher` and `handler` for custom URLs handling.
         *
         * The `matcher` can be:
         *
         * - a [[UrlMatcher]]: See: [[UrlMatcherFactory.compile]]
         * - a `string`: The string is compiled to a [[UrlMatcher]]
         * - a `RegExp`: The regexp is used to match the url.
         *
         * The `handler` can be:
         *
         * - a string: The url is redirected to the value of the string.
         * - a function: The url is redirected to the return value of the function.
         *
         * ---
         *
         * When the `handler` is a `string` and the `matcher` is a `UrlMatcher` (or string), the redirect
         * string is interpolated with parameter values.
         *
         * #### Example:
         * When the URL is `/foo/123` the rule will redirect to `/bar/123`.
         * ```js
         * .when("/foo/:param1", "/bar/:param1")
         * ```
         *
         * ---
         *
         * When the `handler` is a string and the `matcher` is a `RegExp`, the redirect string is
         * interpolated with capture groups from the RegExp.
         *
         * #### Example:
         * When the URL is `/foo/123` the rule will redirect to `/bar/123`.
         * ```js
         * .when(new RegExp("^/foo/(.*)$"), "/bar/$1");
         * ```
         *
         * ---
         *
         * When the handler is a function, it receives the matched value, the current URL, and the `UIRouter` object (See [[UrlRuleHandlerFn]]).
         * The "matched value" differs based on the `matcher`.
         * For [[UrlMatcher]]s, it will be the matched state params.
         * For `RegExp`, it will be the match array from `regexp.exec()`.
         *
         * If the handler returns a string, the URL is redirected to the string.
         *
         * #### Example:
         * When the URL is `/foo/123` the rule will redirect to `/bar/123`.
         * ```js
         * .when(new RegExp("^/foo/(.*)$"), match => "/bar/" + match[1]);
         * ```
         *
         * Note: the `handler` may also invoke arbitrary code, such as `$state.go()`
         *
         * @param matcher A pattern `string` to match, compiled as a [[UrlMatcher]], or a `RegExp`.
         * @param handler The path to redirect to, or a function that returns the path.
         * @param options `{ priority: number }`
         *
         * @return the registered [[UrlRule]]
         */
        UrlRules.prototype.when = function (matcher, handler, options) {
            var rule = this.urlRuleFactory.create(matcher, handler);
            if (isDefined(options && options.priority))
                rule.priority = options.priority;
            this.rule(rule);
            return rule;
        };
        return UrlRules;
    }());

    /**
     * An API to customize the URL behavior and retrieve URL configuration
     *
     * This API is used to customize the behavior of the URL.
     * This includes optional trailing slashes ([[strictMode]]), case sensitivity ([[caseInsensitive]]),
     * and custom parameter encoding (custom [[type]]).
     *
     * It also has information about the location (url) configuration such as [[port]] and [[baseHref]].
     * This information can be used to build absolute URLs, such as
     * `https://example.com:443/basepath/state/substate?param1=a#hashvalue`;
     *
     * This API is found at `router.urlService.config` (see: [[UIRouter.urlService]], [[URLService.config]])
     */
    var UrlConfig = /** @class */ (function () {
        /** @internal */ function UrlConfig(/** @internal */ router) {
            var _this = this;
            this.router = router;
            /** @internal */ this.paramTypes = new ParamTypes();
            /** @internal */ this._decodeParams = true;
            /** @internal */ this._isCaseInsensitive = false;
            /** @internal */ this._isStrictMode = true;
            /** @internal */ this._defaultSquashPolicy = false;
            /** @internal */ this.dispose = function () { return _this.paramTypes.dispose(); };
            // Delegate these calls to the current LocationConfig implementation
            /**
             * Gets the base Href, e.g., `http://localhost/approot/`
             *
             * @return the application's base href
             */
            this.baseHref = function () { return _this.router.locationConfig.baseHref(); };
            /**
             * Gets or sets the hashPrefix
             *
             * This only applies when not running in [[html5Mode]] (pushstate mode)
             *
             * If the current url is `http://localhost/app#!/uirouter/path/#anchor`, it returns `!` which is the prefix for the "hashbang" portion.
             *
             * @return the hash prefix
             */
            this.hashPrefix = function (newprefix) { return _this.router.locationConfig.hashPrefix(newprefix); };
            /**
             * Gets the host, e.g., `localhost`
             *
             * @return the protocol
             */
            this.host = function () { return _this.router.locationConfig.host(); };
            /**
             * Returns true when running in pushstate mode
             *
             * @return true when running in html5 mode (pushstate mode).
             */
            this.html5Mode = function () { return _this.router.locationConfig.html5Mode(); };
            /**
             * Gets the port, e.g., `80`
             *
             * @return the port number
             */
            this.port = function () { return _this.router.locationConfig.port(); };
            /**
             * Gets the protocol, e.g., `http`
             *
             * @return the protocol
             */
            this.protocol = function () { return _this.router.locationConfig.protocol(); };
        }
        /**
         * Defines whether URL matching should be case sensitive (the default behavior), or not.
         *
         * #### Example:
         * ```js
         * // Allow case insensitive url matches
         * urlService.config.caseInsensitive(true);
         * ```
         *
         * @param value `false` to match URL in a case sensitive manner; otherwise `true`;
         * @returns the current value of caseInsensitive
         */
        UrlConfig.prototype.caseInsensitive = function (value) {
            return (this._isCaseInsensitive = isDefined(value) ? value : this._isCaseInsensitive);
        };
        /**
         * Sets the default behavior when generating or matching URLs with default parameter values.
         *
         * #### Example:
         * ```js
         * // Remove default parameter values from the url
         * urlService.config.defaultSquashPolicy(true);
         * ```
         *
         * @param value A string that defines the default parameter URL squashing behavior.
         *    - `nosquash`: When generating an href with a default parameter value, do not squash the parameter value from the URL
         *    - `slash`: When generating an href with a default parameter value, squash (remove) the parameter value, and, if the
         *      parameter is surrounded by slashes, squash (remove) one slash from the URL
         *    - any other string, e.g. "~": When generating an href with a default parameter value, squash (remove)
         *      the parameter value from the URL and replace it with this string.
         * @returns the current value of defaultSquashPolicy
         */
        UrlConfig.prototype.defaultSquashPolicy = function (value) {
            if (isDefined(value) && value !== true && value !== false && !isString(value))
                throw new Error("Invalid squash policy: " + value + ". Valid policies: false, true, arbitrary-string");
            return (this._defaultSquashPolicy = isDefined(value) ? value : this._defaultSquashPolicy);
        };
        /**
         * Defines whether URLs should match trailing slashes, or not (the default behavior).
         *
         * #### Example:
         * ```js
         * // Allow optional trailing slashes
         * urlService.config.strictMode(false);
         * ```
         *
         * @param value `false` to match trailing slashes in URLs, otherwise `true`.
         * @returns the current value of strictMode
         */
        UrlConfig.prototype.strictMode = function (value) {
            return (this._isStrictMode = isDefined(value) ? value : this._isStrictMode);
        };
        /**
         * Creates and registers a custom [[ParamType]] object
         *
         * A custom parameter type can be used to generate URLs with typed parameters or custom encoding/decoding.
         *
         * #### Note: Register custom types *before using them* in a state definition.
         *
         * #### Example:
         * ```js
         * // Encode object parameter as JSON string
         * urlService.config.type('myjson', {
         *   encode: (obj) => JSON.stringify(obj),
         *   decode: (str) => JSON.parse(str),
         *   is: (val) => typeof(val) === 'object',
         *   pattern: /[^/]+/,
         *   equals: (a, b) => _.isEqual(a, b),
         * });
         * ```
         *
         * See [[ParamTypeDefinition]] for more examples
         *
         * @param name The type name.
         * @param definition The type definition. See [[ParamTypeDefinition]] for information on the values accepted.
         * @param definitionFn A function that is injected before the app runtime starts.
         *        The result of this function should be a [[ParamTypeDefinition]].
         *        The result is merged into the existing `definition`.
         *        See [[ParamType]] for information on the values accepted.
         *
         * @returns if only the `name` parameter was specified: the currently registered [[ParamType]] object, or undefined
         */
        UrlConfig.prototype.type = function (name, definition, definitionFn) {
            var type = this.paramTypes.type(name, definition, definitionFn);
            return !isDefined(definition) ? type : this;
        };
        return UrlConfig;
    }());

    /**
     * API for URL management
     */
    var UrlService = /** @class */ (function () {
        /** @internal */
        function UrlService(/** @internal */ router) {
            var _this = this;
            this.router = router;
            /** @internal */ this.interceptDeferred = false;
            /**
             * The nested [[UrlRules]] API for managing URL rules and rewrites
             *
             * See: [[UrlRules]] for details
             */
            this.rules = new UrlRules(this.router);
            /**
             * The nested [[UrlConfig]] API to configure the URL and retrieve URL information
             *
             * See: [[UrlConfig]] for details
             */
            this.config = new UrlConfig(this.router);
            // Delegate these calls to the current LocationServices implementation
            /**
             * Gets the current url, or updates the url
             *
             * ### Getting the current URL
             *
             * When no arguments are passed, returns the current URL.
             * The URL is normalized using the internal [[path]]/[[search]]/[[hash]] values.
             *
             * For example, the URL may be stored in the hash ([[HashLocationServices]]) or
             * have a base HREF prepended ([[PushStateLocationServices]]).
             *
             * The raw URL in the browser might be:
             *
             * ```
             * http://mysite.com/somepath/index.html#/internal/path/123?param1=foo#anchor
             * ```
             *
             * or
             *
             * ```
             * http://mysite.com/basepath/internal/path/123?param1=foo#anchor
             * ```
             *
             * then this method returns:
             *
             * ```
             * /internal/path/123?param1=foo#anchor
             * ```
             *
             *
             * #### Example:
             * ```js
             * locationServices.url(); // "/some/path?query=value#anchor"
             * ```
             *
             * ### Updating the URL
             *
             * When `newurl` arguments is provided, changes the URL to reflect `newurl`
             *
             * #### Example:
             * ```js
             * locationServices.url("/some/path?query=value#anchor", true);
             * ```
             *
             * @param newurl The new value for the URL.
             *               This url should reflect only the new internal [[path]], [[search]], and [[hash]] values.
             *               It should not include the protocol, site, port, or base path of an absolute HREF.
             * @param replace When true, replaces the current history entry (instead of appending it) with this new url
             * @param state The history's state object, i.e., pushState (if the LocationServices implementation supports it)
             *
             * @return the url (after potentially being processed)
             */
            this.url = function (newurl, replace, state) {
                return _this.router.locationService.url(newurl, replace, state);
            };
            /**
             * Gets the path part of the current url
             *
             * If the current URL is `/some/path?query=value#anchor`, this returns `/some/path`
             *
             * @return the path portion of the url
             */
            this.path = function () { return _this.router.locationService.path(); };
            /**
             * Gets the search part of the current url as an object
             *
             * If the current URL is `/some/path?query=value#anchor`, this returns `{ query: 'value' }`
             *
             * @return the search (query) portion of the url, as an object
             */
            this.search = function () { return _this.router.locationService.search(); };
            /**
             * Gets the hash part of the current url
             *
             * If the current URL is `/some/path?query=value#anchor`, this returns `anchor`
             *
             * @return the hash (anchor) portion of the url
             */
            this.hash = function () { return _this.router.locationService.hash(); };
            /**
             * @internal
             *
             * Registers a low level url change handler
             *
             * Note: Because this is a low level handler, it's not recommended for general use.
             *
             * #### Example:
             * ```js
             * let deregisterFn = locationServices.onChange((evt) => console.log("url change", evt));
             * ```
             *
             * @param callback a function that will be called when the url is changing
             * @return a function that de-registers the callback
             */
            this.onChange = function (callback) { return _this.router.locationService.onChange(callback); };
        }
        /** @internal */
        UrlService.prototype.dispose = function () {
            this.listen(false);
            this.rules.dispose();
        };
        /**
         * Gets the current URL parts
         *
         * This method returns the different parts of the current URL (the [[path]], [[search]], and [[hash]]) as a [[UrlParts]] object.
         */
        UrlService.prototype.parts = function () {
            return { path: this.path(), search: this.search(), hash: this.hash() };
        };
        /**
         * Activates the best rule for the current URL
         *
         * Checks the current URL for a matching [[UrlRule]], then invokes that rule's handler.
         * This method is called internally any time the URL has changed.
         *
         * This effectively activates the state (or redirect, etc) which matches the current URL.
         *
         * #### Example:
         * ```js
         * urlService.deferIntercept();
         *
         * fetch('/states.json').then(resp => resp.json()).then(data => {
         *   data.forEach(state => $stateRegistry.register(state));
         *   urlService.listen();
         *   // Find the matching URL and invoke the handler.
         *   urlService.sync();
         * });
         * ```
         */
        UrlService.prototype.sync = function (evt) {
            if (evt && evt.defaultPrevented)
                return;
            var _a = this.router, urlService = _a.urlService, stateService = _a.stateService;
            var url = { path: urlService.path(), search: urlService.search(), hash: urlService.hash() };
            var best = this.match(url);
            var applyResult = pattern([
                [isString, function (newurl) { return urlService.url(newurl, true); }],
                [TargetState.isDef, function (def) { return stateService.go(def.state, def.params, def.options); }],
                [is(TargetState), function (target) { return stateService.go(target.state(), target.params(), target.options()); }],
            ]);
            applyResult(best && best.rule.handler(best.match, url, this.router));
        };
        /**
         * Starts or stops listening for URL changes
         *
         * Call this sometime after calling [[deferIntercept]] to start monitoring the url.
         * This causes UI-Router to start listening for changes to the URL, if it wasn't already listening.
         *
         * If called with `false`, UI-Router will stop listening (call listen(true) to start listening again).
         *
         * #### Example:
         * ```js
         * urlService.deferIntercept();
         *
         * fetch('/states.json').then(resp => resp.json()).then(data => {
         *   data.forEach(state => $stateRegistry.register(state));
         *   // Start responding to URL changes
         *   urlService.listen();
         *   urlService.sync();
         * });
         * ```
         *
         * @param enabled `true` or `false` to start or stop listening to URL changes
         */
        UrlService.prototype.listen = function (enabled) {
            var _this = this;
            if (enabled === false) {
                this._stopListeningFn && this._stopListeningFn();
                delete this._stopListeningFn;
            }
            else {
                return (this._stopListeningFn =
                    this._stopListeningFn || this.router.urlService.onChange(function (evt) { return _this.sync(evt); }));
            }
        };
        /**
         * Disables monitoring of the URL.
         *
         * Call this method before UI-Router has bootstrapped.
         * It will stop UI-Router from performing the initial url sync.
         *
         * This can be useful to perform some asynchronous initialization before the router starts.
         * Once the initialization is complete, call [[listen]] to tell UI-Router to start watching and synchronizing the URL.
         *
         * #### Example:
         * ```js
         * // Prevent UI-Router from automatically intercepting URL changes when it starts;
         * urlService.deferIntercept();
         *
         * fetch('/states.json').then(resp => resp.json()).then(data => {
         *   data.forEach(state => $stateRegistry.register(state));
         *   urlService.listen();
         *   urlService.sync();
         * });
         * ```
         *
         * @param defer Indicates whether to defer location change interception.
         *        Passing no parameter is equivalent to `true`.
         */
        UrlService.prototype.deferIntercept = function (defer) {
            if (defer === undefined)
                defer = true;
            this.interceptDeferred = defer;
        };
        /**
         * Matches a URL
         *
         * Given a URL (as a [[UrlParts]] object), check all rules and determine the best matching rule.
         * Return the result as a [[MatchResult]].
         */
        UrlService.prototype.match = function (url) {
            var _this = this;
            url = extend({ path: '', search: {}, hash: '' }, url);
            var rules = this.rules.rules();
            // Checks a single rule. Returns { rule: rule, match: match, weight: weight } if it matched, or undefined
            var checkRule = function (rule) {
                var match = rule.match(url, _this.router);
                return match && { match: match, rule: rule, weight: rule.matchPriority(match) };
            };
            // The rules are pre-sorted.
            // - Find the first matching rule.
            // - Find any other matching rule that sorted *exactly the same*, according to `.sort()`.
            // - Choose the rule with the highest match weight.
            var best;
            for (var i = 0; i < rules.length; i++) {
                // Stop when there is a 'best' rule and the next rule sorts differently than it.
                if (best && best.rule._group !== rules[i]._group)
                    break;
                var current = checkRule(rules[i]);
                // Pick the best MatchResult
                best = !best || (current && current.weight > best.weight) ? current : best;
            }
            return best;
        };
        return UrlService;
    }());

    /** @internal */
    var _routerInstance = 0;
    /** @internal */
    var locSvcFns = ['url', 'path', 'search', 'hash', 'onChange'];
    /** @internal */
    var locCfgFns = ['port', 'protocol', 'host', 'baseHref', 'html5Mode', 'hashPrefix'];
    /** @internal */
    var locationServiceStub = makeStub('LocationServices', locSvcFns);
    /** @internal */
    var locationConfigStub = makeStub('LocationConfig', locCfgFns);
    /**
     * An instance of UI-Router.
     *
     * This object contains references to service APIs which define your application's routing behavior.
     */
    var UIRouter = /** @class */ (function () {
        /**
         * Creates a new `UIRouter` object
         *
         * @param locationService a [[LocationServices]] implementation
         * @param locationConfig a [[LocationConfig]] implementation
         * @internal
         */
        function UIRouter(locationService, locationConfig) {
            if (locationService === void 0) { locationService = locationServiceStub; }
            if (locationConfig === void 0) { locationConfig = locationConfigStub; }
            this.locationService = locationService;
            this.locationConfig = locationConfig;
            /** @internal */ this.$id = _routerInstance++;
            /** @internal */ this._disposed = false;
            /** @internal */ this._disposables = [];
            /** Enable/disable tracing to the javascript console */
            this.trace = trace;
            /** Provides services related to ui-view synchronization */
            this.viewService = new ViewService(this);
            /** An object that contains global router state, such as the current state and params */
            this.globals = new UIRouterGlobals();
            /** A service that exposes global Transition Hooks */
            this.transitionService = new TransitionService(this);
            /**
             * Deprecated for public use. Use [[urlService]] instead.
             * @deprecated Use [[urlService]] instead
             */
            this.urlMatcherFactory = new UrlMatcherFactory(this);
            /**
             * Deprecated for public use. Use [[urlService]] instead.
             * @deprecated Use [[urlService]] instead
             */
            this.urlRouter = new UrlRouter(this);
            /** Provides services related to the URL */
            this.urlService = new UrlService(this);
            /** Provides a registry for states, and related registration services */
            this.stateRegistry = new StateRegistry(this);
            /** Provides services related to states */
            this.stateService = new StateService(this);
            /** @internal plugin instances are registered here */
            this._plugins = {};
            this.viewService._pluginapi._rootViewContext(this.stateRegistry.root());
            this.globals.$current = this.stateRegistry.root();
            this.globals.current = this.globals.$current.self;
            this.disposable(this.globals);
            this.disposable(this.stateService);
            this.disposable(this.stateRegistry);
            this.disposable(this.transitionService);
            this.disposable(this.urlService);
            this.disposable(locationService);
            this.disposable(locationConfig);
        }
        /** Registers an object to be notified when the router is disposed */
        UIRouter.prototype.disposable = function (disposable) {
            this._disposables.push(disposable);
        };
        /**
         * Disposes this router instance
         *
         * When called, clears resources retained by the router by calling `dispose(this)` on all
         * registered [[disposable]] objects.
         *
         * Or, if a `disposable` object is provided, calls `dispose(this)` on that object only.
         *
         * @internal
         * @param disposable (optional) the disposable to dispose
         */
        UIRouter.prototype.dispose = function (disposable) {
            var _this = this;
            if (disposable && isFunction(disposable.dispose)) {
                disposable.dispose(this);
                return undefined;
            }
            this._disposed = true;
            this._disposables.slice().forEach(function (d) {
                try {
                    typeof d.dispose === 'function' && d.dispose(_this);
                    removeFrom(_this._disposables, d);
                }
                catch (ignored) { }
            });
        };
        /**
         * Adds a plugin to UI-Router
         *
         * This method adds a UI-Router Plugin.
         * A plugin can enhance or change UI-Router behavior using any public API.
         *
         * #### Example:
         * ```js
         * import { MyCoolPlugin } from "ui-router-cool-plugin";
         *
         * var plugin = router.addPlugin(MyCoolPlugin);
         * ```
         *
         * ### Plugin authoring
         *
         * A plugin is simply a class (or constructor function) which accepts a [[UIRouter]] instance and (optionally) an options object.
         *
         * The plugin can implement its functionality using any of the public APIs of [[UIRouter]].
         * For example, it may configure router options or add a Transition Hook.
         *
         * The plugin can then be published as a separate module.
         *
         * #### Example:
         * ```js
         * export class MyAuthPlugin implements UIRouterPlugin {
         *   constructor(router: UIRouter, options: any) {
         *     this.name = "MyAuthPlugin";
         *     let $transitions = router.transitionService;
         *     let $state = router.stateService;
         *
         *     let authCriteria = {
         *       to: (state) => state.data && state.data.requiresAuth
         *     };
         *
         *     function authHook(transition: Transition) {
         *       let authService = transition.injector().get('AuthService');
         *       if (!authService.isAuthenticated()) {
         *         return $state.target('login');
         *       }
         *     }
         *
         *     $transitions.onStart(authCriteria, authHook);
         *   }
         * }
         * ```
         *
         * @param plugin one of:
         *        - a plugin class which implements [[UIRouterPlugin]]
         *        - a constructor function for a [[UIRouterPlugin]] which accepts a [[UIRouter]] instance
         *        - a factory function which accepts a [[UIRouter]] instance and returns a [[UIRouterPlugin]] instance
         * @param options options to pass to the plugin class/factory
         * @returns the registered plugin instance
         */
        UIRouter.prototype.plugin = function (plugin, options) {
            if (options === void 0) { options = {}; }
            var pluginInstance = new plugin(this, options);
            if (!pluginInstance.name)
                throw new Error('Required property `name` missing on plugin: ' + pluginInstance);
            this._disposables.push(pluginInstance);
            return (this._plugins[pluginInstance.name] = pluginInstance);
        };
        UIRouter.prototype.getPlugin = function (pluginName) {
            return pluginName ? this._plugins[pluginName] : values(this._plugins);
        };
        return UIRouter;
    }());

    function addCoreResolvables(trans) {
        trans.addResolvable(Resolvable.fromData(UIRouter, trans.router), '');
        trans.addResolvable(Resolvable.fromData(Transition, trans), '');
        trans.addResolvable(Resolvable.fromData('$transition$', trans), '');
        trans.addResolvable(Resolvable.fromData('$stateParams', trans.params()), '');
        trans.entering().forEach(function (state) {
            trans.addResolvable(Resolvable.fromData('$state$', state), state);
        });
    }
    var registerAddCoreResolvables = function (transitionService) {
        return transitionService.onCreate({}, addCoreResolvables);
    };
    var TRANSITION_TOKENS = ['$transition$', Transition];
    var isTransition = inArray(TRANSITION_TOKENS);
    // References to Transition in the treeChanges pathnodes makes all
    // previous Transitions reachable in memory, causing a memory leak
    // This function removes resolves for '$transition$' and `Transition` from the treeChanges.
    // Do not use this on current transitions, only on old ones.
    var treeChangesCleanup = function (trans) {
        var nodes = values(trans.treeChanges()).reduce(unnestR, []).reduce(uniqR, []);
        // If the resolvable is a Transition, return a new resolvable with null data
        var replaceTransitionWithNull = function (r) {
            return isTransition(r.token) ? Resolvable.fromData(r.token, null) : r;
        };
        nodes.forEach(function (node) {
            node.resolvables = node.resolvables.map(replaceTransitionWithNull);
        });
    };

    /**
     * A [[TransitionHookFn]] that redirects to a different state or params
     *
     * Registered using `transitionService.onStart({ to: (state) => !!state.redirectTo }, redirectHook);`
     *
     * See [[StateDeclaration.redirectTo]]
     */
    var redirectToHook = function (trans) {
        var redirect = trans.to().redirectTo;
        if (!redirect)
            return;
        var $state = trans.router.stateService;
        function handleResult(result) {
            if (!result)
                return;
            if (result instanceof TargetState)
                return result;
            if (isString(result))
                return $state.target(result, trans.params(), trans.options());
            if (result['state'] || result['params'])
                return $state.target(result['state'] || trans.to(), result['params'] || trans.params(), trans.options());
        }
        if (isFunction(redirect)) {
            return services.$q.when(redirect(trans)).then(handleResult);
        }
        return handleResult(redirect);
    };
    var registerRedirectToHook = function (transitionService) {
        return transitionService.onStart({ to: function (state) { return !!state.redirectTo; } }, redirectToHook);
    };

    /**
     * A factory which creates an onEnter, onExit or onRetain transition hook function
     *
     * The returned function invokes the (for instance) state.onEnter hook when the
     * state is being entered.
     */
    function makeEnterExitRetainHook(hookName) {
        return function (transition, state) {
            var _state = state.$$state();
            var hookFn = _state[hookName];
            return hookFn(transition, state);
        };
    }
    /**
     * The [[TransitionStateHookFn]] for onExit
     *
     * When the state is being exited, the state's .onExit function is invoked.
     *
     * Registered using `transitionService.onExit({ exiting: (state) => !!state.onExit }, onExitHook);`
     *
     * See: [[IHookRegistry.onExit]]
     */
    var onExitHook = makeEnterExitRetainHook('onExit');
    var registerOnExitHook = function (transitionService) {
        return transitionService.onExit({ exiting: function (state) { return !!state.onExit; } }, onExitHook);
    };
    /**
     * The [[TransitionStateHookFn]] for onRetain
     *
     * When the state was already entered, and is not being exited or re-entered, the state's .onRetain function is invoked.
     *
     * Registered using `transitionService.onRetain({ retained: (state) => !!state.onRetain }, onRetainHook);`
     *
     * See: [[IHookRegistry.onRetain]]
     */
    var onRetainHook = makeEnterExitRetainHook('onRetain');
    var registerOnRetainHook = function (transitionService) {
        return transitionService.onRetain({ retained: function (state) { return !!state.onRetain; } }, onRetainHook);
    };
    /**
     * The [[TransitionStateHookFn]] for onEnter
     *
     * When the state is being entered, the state's .onEnter function is invoked.
     *
     * Registered using `transitionService.onEnter({ entering: (state) => !!state.onEnter }, onEnterHook);`
     *
     * See: [[IHookRegistry.onEnter]]
     */
    var onEnterHook = makeEnterExitRetainHook('onEnter');
    var registerOnEnterHook = function (transitionService) {
        return transitionService.onEnter({ entering: function (state) { return !!state.onEnter; } }, onEnterHook);
    };

    var RESOLVE_HOOK_PRIORITY = 1000;
    /**
     * A [[TransitionHookFn]] which resolves all EAGER Resolvables in the To Path
     *
     * Registered using `transitionService.onStart({}, eagerResolvePath, { priority: 1000 });`
     *
     * When a Transition starts, this hook resolves all the EAGER Resolvables, which the transition then waits for.
     *
     * See [[StateDeclaration.resolve]]
     */
    var eagerResolvePath = function (trans) {
        return new ResolveContext(trans.treeChanges().to).resolvePath('EAGER', trans).then(noop);
    };
    var registerEagerResolvePath = function (transitionService) {
        return transitionService.onStart({}, eagerResolvePath, { priority: RESOLVE_HOOK_PRIORITY });
    };
    /**
     * A [[TransitionHookFn]] which resolves all LAZY Resolvables for the state (and all its ancestors) in the To Path
     *
     * Registered using `transitionService.onEnter({ entering: () => true }, lazyResolveState, { priority: 1000 });`
     *
     * When a State is being entered, this hook resolves all the Resolvables for this state, which the transition then waits for.
     *
     * See [[StateDeclaration.resolve]]
     */
    var lazyResolveState = function (trans, state) {
        return new ResolveContext(trans.treeChanges().to).subContext(state.$$state()).resolvePath('LAZY', trans).then(noop);
    };
    var registerLazyResolveState = function (transitionService) {
        return transitionService.onEnter({ entering: val(true) }, lazyResolveState, { priority: RESOLVE_HOOK_PRIORITY });
    };
    /**
     * A [[TransitionHookFn]] which resolves any dynamically added (LAZY or EAGER) Resolvables.
     *
     * Registered using `transitionService.onFinish({}, eagerResolvePath, { priority: 1000 });`
     *
     * After all entering states have been entered, this hook resolves any remaining Resolvables.
     * These are typically dynamic resolves which were added by some Transition Hook using [[Transition.addResolvable]].
     *
     * See [[StateDeclaration.resolve]]
     */
    var resolveRemaining = function (trans) {
        return new ResolveContext(trans.treeChanges().to).resolvePath('LAZY', trans).then(noop);
    };
    var registerResolveRemaining = function (transitionService) {
        return transitionService.onFinish({}, resolveRemaining, { priority: RESOLVE_HOOK_PRIORITY });
    };

    /**
     * A [[TransitionHookFn]] which waits for the views to load
     *
     * Registered using `transitionService.onStart({}, loadEnteringViews);`
     *
     * Allows the views to do async work in [[ViewConfig.load]] before the transition continues.
     * In angular 1, this includes loading the templates.
     */
    var loadEnteringViews = function (transition) {
        var $q = services.$q;
        var enteringViews = transition.views('entering');
        if (!enteringViews.length)
            return;
        return $q.all(enteringViews.map(function (view) { return $q.when(view.load()); })).then(noop);
    };
    var registerLoadEnteringViews = function (transitionService) {
        return transitionService.onFinish({}, loadEnteringViews);
    };
    /**
     * A [[TransitionHookFn]] which activates the new views when a transition is successful.
     *
     * Registered using `transitionService.onSuccess({}, activateViews);`
     *
     * After a transition is complete, this hook deactivates the old views from the previous state,
     * and activates the new views from the destination state.
     *
     * See [[ViewService]]
     */
    var activateViews = function (transition) {
        var enteringViews = transition.views('entering');
        var exitingViews = transition.views('exiting');
        if (!enteringViews.length && !exitingViews.length)
            return;
        var $view = transition.router.viewService;
        exitingViews.forEach(function (vc) { return $view.deactivateViewConfig(vc); });
        enteringViews.forEach(function (vc) { return $view.activateViewConfig(vc); });
        $view.sync();
    };
    var registerActivateViews = function (transitionService) {
        return transitionService.onSuccess({}, activateViews);
    };

    /**
     * A [[TransitionHookFn]] which updates global UI-Router state
     *
     * Registered using `transitionService.onBefore({}, updateGlobalState);`
     *
     * Before a [[Transition]] starts, updates the global value of "the current transition" ([[Globals.transition]]).
     * After a successful [[Transition]], updates the global values of "the current state"
     * ([[Globals.current]] and [[Globals.$current]]) and "the current param values" ([[Globals.params]]).
     *
     * See also the deprecated properties:
     * [[StateService.transition]], [[StateService.current]], [[StateService.params]]
     */
    var updateGlobalState = function (trans) {
        var globals = trans.router.globals;
        var transitionSuccessful = function () {
            globals.successfulTransitions.enqueue(trans);
            globals.$current = trans.$to();
            globals.current = globals.$current.self;
            copy(trans.params(), globals.params);
        };
        var clearCurrentTransition = function () {
            // Do not clear globals.transition if a different transition has started in the meantime
            if (globals.transition === trans)
                globals.transition = null;
        };
        trans.onSuccess({}, transitionSuccessful, { priority: 10000 });
        trans.promise.then(clearCurrentTransition, clearCurrentTransition);
    };
    var registerUpdateGlobalState = function (transitionService) {
        return transitionService.onCreate({}, updateGlobalState);
    };

    /**
     * A [[TransitionHookFn]] which updates the URL after a successful transition
     *
     * Registered using `transitionService.onSuccess({}, updateUrl);`
     */
    var updateUrl = function (transition) {
        var options = transition.options();
        var $state = transition.router.stateService;
        var $urlRouter = transition.router.urlRouter;
        // Dont update the url in these situations:
        // The transition was triggered by a URL sync (options.source === 'url')
        // The user doesn't want the url to update (options.location === false)
        // The destination state, and all parents have no navigable url
        if (options.source !== 'url' && options.location && $state.$current.navigable) {
            var urlOptions = { replace: options.location === 'replace' };
            $urlRouter.push($state.$current.navigable.url, $state.params, urlOptions);
        }
        $urlRouter.update(true);
    };
    var registerUpdateUrl = function (transitionService) {
        return transitionService.onSuccess({}, updateUrl, { priority: 9999 });
    };

    /**
     * A [[TransitionHookFn]] that performs lazy loading
     *
     * When entering a state "abc" which has a `lazyLoad` function defined:
     * - Invoke the `lazyLoad` function (unless it is already in process)
     *   - Flag the hook function as "in process"
     *   - The function should return a promise (that resolves when lazy loading is complete)
     * - Wait for the promise to settle
     *   - If the promise resolves to a [[LazyLoadResult]], then register those states
     *   - Flag the hook function as "not in process"
     * - If the hook was successful
     *   - Remove the `lazyLoad` function from the state declaration
     * - If all the hooks were successful
     *   - Retry the transition (by returning a TargetState)
     *
     * ```
     * .state('abc', {
     *   component: 'fooComponent',
     *   lazyLoad: () => import('./fooComponent')
     *   });
     * ```
     *
     * See [[StateDeclaration.lazyLoad]]
     */
    var lazyLoadHook = function (transition) {
        var router = transition.router;
        function retryTransition() {
            if (transition.originalTransition().options().source !== 'url') {
                // The original transition was not triggered via url sync
                // The lazy state should be loaded now, so re-try the original transition
                var orig = transition.targetState();
                return router.stateService.target(orig.identifier(), orig.params(), orig.options());
            }
            // The original transition was triggered via url sync
            // Run the URL rules and find the best match
            var $url = router.urlService;
            var result = $url.match($url.parts());
            var rule = result && result.rule;
            // If the best match is a state, redirect the transition (instead
            // of calling sync() which supersedes the current transition)
            if (rule && rule.type === 'STATE') {
                var state = rule.state;
                var params = result.match;
                return router.stateService.target(state, params, transition.options());
            }
            // No matching state found, so let .sync() choose the best non-state match/otherwise
            router.urlService.sync();
        }
        var promises = transition
            .entering()
            .filter(function (state) { return !!state.$$state().lazyLoad; })
            .map(function (state) { return lazyLoadState(transition, state); });
        return services.$q.all(promises).then(retryTransition);
    };
    var registerLazyLoadHook = function (transitionService) {
        return transitionService.onBefore({ entering: function (state) { return !!state.lazyLoad; } }, lazyLoadHook);
    };
    /**
     * Invokes a state's lazy load function
     *
     * @param transition a Transition context
     * @param state the state to lazy load
     * @returns A promise for the lazy load result
     */
    function lazyLoadState(transition, state) {
        var lazyLoadFn = state.$$state().lazyLoad;
        // Store/get the lazy load promise on/from the hookfn so it doesn't get re-invoked
        var promise = lazyLoadFn['_promise'];
        if (!promise) {
            var success = function (result) {
                delete state.lazyLoad;
                delete state.$$state().lazyLoad;
                delete lazyLoadFn['_promise'];
                return result;
            };
            var error = function (err) {
                delete lazyLoadFn['_promise'];
                return services.$q.reject(err);
            };
            promise = lazyLoadFn['_promise'] = services.$q
                .when(lazyLoadFn(transition, state))
                .then(updateStateRegistry)
                .then(success, error);
        }
        /** Register any lazy loaded state definitions */
        function updateStateRegistry(result) {
            if (result && Array.isArray(result.states)) {
                result.states.forEach(function (_state) { return transition.router.stateRegistry.register(_state); });
            }
            return result;
        }
        return promise;
    }

    /**
     * This class defines a type of hook, such as `onBefore` or `onEnter`.
     * Plugins can define custom hook types, such as sticky states does for `onInactive`.
     */
    var TransitionEventType = /** @class */ (function () {
        /* tslint:disable:no-inferrable-types */
        function TransitionEventType(name, hookPhase, hookOrder, criteriaMatchPath, reverseSort, getResultHandler, getErrorHandler, synchronous) {
            if (reverseSort === void 0) { reverseSort = false; }
            if (getResultHandler === void 0) { getResultHandler = TransitionHook.HANDLE_RESULT; }
            if (getErrorHandler === void 0) { getErrorHandler = TransitionHook.REJECT_ERROR; }
            if (synchronous === void 0) { synchronous = false; }
            this.name = name;
            this.hookPhase = hookPhase;
            this.hookOrder = hookOrder;
            this.criteriaMatchPath = criteriaMatchPath;
            this.reverseSort = reverseSort;
            this.getResultHandler = getResultHandler;
            this.getErrorHandler = getErrorHandler;
            this.synchronous = synchronous;
        }
        return TransitionEventType;
    }());

    /**
     * A [[TransitionHookFn]] that skips a transition if it should be ignored
     *
     * This hook is invoked at the end of the onBefore phase.
     *
     * If the transition should be ignored (because no parameter or states changed)
     * then the transition is ignored and not processed.
     */
    function ignoredHook(trans) {
        var ignoredReason = trans._ignoredReason();
        if (!ignoredReason)
            return;
        trace.traceTransitionIgnored(trans);
        var pending = trans.router.globals.transition;
        // The user clicked a link going back to the *current state* ('A')
        // However, there is also a pending transition in flight (to 'B')
        // Abort the transition to 'B' because the user now wants to be back at 'A'.
        if (ignoredReason === 'SameAsCurrent' && pending) {
            pending.abort();
        }
        return Rejection.ignored().toPromise();
    }
    var registerIgnoredTransitionHook = function (transitionService) {
        return transitionService.onBefore({}, ignoredHook, { priority: -9999 });
    };

    /**
     * A [[TransitionHookFn]] that rejects the Transition if it is invalid
     *
     * This hook is invoked at the end of the onBefore phase.
     * If the transition is invalid (for example, param values do not validate)
     * then the transition is rejected.
     */
    function invalidTransitionHook(trans) {
        if (!trans.valid()) {
            throw new Error(trans.error().toString());
        }
    }
    var registerInvalidTransitionHook = function (transitionService) {
        return transitionService.onBefore({}, invalidTransitionHook, { priority: -10000 });
    };

    /**
     * The default [[Transition]] options.
     *
     * Include this object when applying custom defaults:
     * let reloadOpts = { reload: true, notify: true }
     * let options = defaults(theirOpts, customDefaults, defaultOptions);
     */
    var defaultTransOpts = {
        location: true,
        relative: null,
        inherit: false,
        notify: true,
        reload: false,
        supercede: true,
        custom: {},
        current: function () { return null; },
        source: 'unknown',
    };
    /**
     * This class provides services related to Transitions.
     *
     * - Most importantly, it allows global Transition Hooks to be registered.
     * - It allows the default transition error handler to be set.
     * - It also has a factory function for creating new [[Transition]] objects, (used internally by the [[StateService]]).
     *
     * At bootstrap, [[UIRouter]] creates a single instance (singleton) of this class.
     *
     * This API is located at `router.transitionService` ([[UIRouter.transitionService]])
     */
    var TransitionService = /** @class */ (function () {
        /** @internal */
        function TransitionService(_router) {
            /** @internal */
            this._transitionCount = 0;
            /** The transition hook types, such as `onEnter`, `onStart`, etc */
            this._eventTypes = [];
            /** @internal The registered transition hooks */
            this._registeredHooks = {};
            /** The  paths on a criteria object */
            this._criteriaPaths = {};
            this._router = _router;
            this.$view = _router.viewService;
            this._deregisterHookFns = {};
            this._pluginapi = (createProxyFunctions(val(this), {}, val(this), [
                '_definePathType',
                '_defineEvent',
                '_getPathTypes',
                '_getEvents',
                'getHooks',
            ]));
            this._defineCorePaths();
            this._defineCoreEvents();
            this._registerCoreTransitionHooks();
            _router.globals.successfulTransitions.onEvict(treeChangesCleanup);
        }
        /**
         * Registers a [[TransitionHookFn]], called *while a transition is being constructed*.
         *
         * Registers a transition lifecycle hook, which is invoked during transition construction.
         *
         * This low level hook should only be used by plugins.
         * This can be a useful time for plugins to add resolves or mutate the transition as needed.
         * The Sticky States plugin uses this hook to modify the treechanges.
         *
         * ### Lifecycle
         *
         * `onCreate` hooks are invoked *while a transition is being constructed*.
         *
         * ### Return value
         *
         * The hook's return value is ignored
         *
         * @internal
         * @param criteria defines which Transitions the Hook should be invoked for.
         * @param callback the hook function which will be invoked.
         * @param options the registration options
         * @returns a function which deregisters the hook.
         */
        TransitionService.prototype.onCreate = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        TransitionService.prototype.onBefore = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        TransitionService.prototype.onStart = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        TransitionService.prototype.onExit = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        TransitionService.prototype.onRetain = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        TransitionService.prototype.onEnter = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        TransitionService.prototype.onFinish = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        TransitionService.prototype.onSuccess = function (criteria, callback, options) {
            return;
        };
        /** @inheritdoc */
        TransitionService.prototype.onError = function (criteria, callback, options) {
            return;
        };
        /**
         * dispose
         * @internal
         */
        TransitionService.prototype.dispose = function (router) {
            values(this._registeredHooks).forEach(function (hooksArray) {
                return hooksArray.forEach(function (hook) {
                    hook._deregistered = true;
                    removeFrom(hooksArray, hook);
                });
            });
        };
        /**
         * Creates a new [[Transition]] object
         *
         * This is a factory function for creating new Transition objects.
         * It is used internally by the [[StateService]] and should generally not be called by application code.
         *
         * @internal
         * @param fromPath the path to the current state (the from state)
         * @param targetState the target state (destination)
         * @returns a Transition
         */
        TransitionService.prototype.create = function (fromPath, targetState) {
            return new Transition(fromPath, targetState, this._router);
        };
        /** @internal */
        TransitionService.prototype._defineCoreEvents = function () {
            var Phase = exports.TransitionHookPhase;
            var TH = TransitionHook;
            var paths = this._criteriaPaths;
            var NORMAL_SORT = false, REVERSE_SORT = true;
            var SYNCHRONOUS = true;
            this._defineEvent('onCreate', Phase.CREATE, 0, paths.to, NORMAL_SORT, TH.LOG_REJECTED_RESULT, TH.THROW_ERROR, SYNCHRONOUS);
            this._defineEvent('onBefore', Phase.BEFORE, 0, paths.to);
            this._defineEvent('onStart', Phase.RUN, 0, paths.to);
            this._defineEvent('onExit', Phase.RUN, 100, paths.exiting, REVERSE_SORT);
            this._defineEvent('onRetain', Phase.RUN, 200, paths.retained);
            this._defineEvent('onEnter', Phase.RUN, 300, paths.entering);
            this._defineEvent('onFinish', Phase.RUN, 400, paths.to);
            this._defineEvent('onSuccess', Phase.SUCCESS, 0, paths.to, NORMAL_SORT, TH.LOG_REJECTED_RESULT, TH.LOG_ERROR, SYNCHRONOUS);
            this._defineEvent('onError', Phase.ERROR, 0, paths.to, NORMAL_SORT, TH.LOG_REJECTED_RESULT, TH.LOG_ERROR, SYNCHRONOUS);
        };
        /** @internal */
        TransitionService.prototype._defineCorePaths = function () {
            var STATE = exports.TransitionHookScope.STATE, TRANSITION = exports.TransitionHookScope.TRANSITION;
            this._definePathType('to', TRANSITION);
            this._definePathType('from', TRANSITION);
            this._definePathType('exiting', STATE);
            this._definePathType('retained', STATE);
            this._definePathType('entering', STATE);
        };
        /** @internal */
        TransitionService.prototype._defineEvent = function (name, hookPhase, hookOrder, criteriaMatchPath, reverseSort, getResultHandler, getErrorHandler, synchronous) {
            if (reverseSort === void 0) { reverseSort = false; }
            if (getResultHandler === void 0) { getResultHandler = TransitionHook.HANDLE_RESULT; }
            if (getErrorHandler === void 0) { getErrorHandler = TransitionHook.REJECT_ERROR; }
            if (synchronous === void 0) { synchronous = false; }
            var eventType = new TransitionEventType(name, hookPhase, hookOrder, criteriaMatchPath, reverseSort, getResultHandler, getErrorHandler, synchronous);
            this._eventTypes.push(eventType);
            makeEvent(this, this, eventType);
        };
        /** @internal */
        TransitionService.prototype._getEvents = function (phase) {
            var transitionHookTypes = isDefined(phase)
                ? this._eventTypes.filter(function (type) { return type.hookPhase === phase; })
                : this._eventTypes.slice();
            return transitionHookTypes.sort(function (l, r) {
                var cmpByPhase = l.hookPhase - r.hookPhase;
                return cmpByPhase === 0 ? l.hookOrder - r.hookOrder : cmpByPhase;
            });
        };
        /**
         * Adds a Path to be used as a criterion against a TreeChanges path
         *
         * For example: the `exiting` path in [[HookMatchCriteria]] is a STATE scoped path.
         * It was defined by calling `defineTreeChangesCriterion('exiting', TransitionHookScope.STATE)`
         * Each state in the exiting path is checked against the criteria and returned as part of the match.
         *
         * Another example: the `to` path in [[HookMatchCriteria]] is a TRANSITION scoped path.
         * It was defined by calling `defineTreeChangesCriterion('to', TransitionHookScope.TRANSITION)`
         * Only the tail of the `to` path is checked against the criteria and returned as part of the match.
         *
         * @internal
         */
        TransitionService.prototype._definePathType = function (name, hookScope) {
            this._criteriaPaths[name] = { name: name, scope: hookScope };
        };
        /** @internal */
        // tslint:disable-next-line
        TransitionService.prototype._getPathTypes = function () {
            return this._criteriaPaths;
        };
        /** @internal */
        TransitionService.prototype.getHooks = function (hookName) {
            return this._registeredHooks[hookName];
        };
        /** @internal */
        TransitionService.prototype._registerCoreTransitionHooks = function () {
            var fns = this._deregisterHookFns;
            fns.addCoreResolves = registerAddCoreResolvables(this);
            fns.ignored = registerIgnoredTransitionHook(this);
            fns.invalid = registerInvalidTransitionHook(this);
            // Wire up redirectTo hook
            fns.redirectTo = registerRedirectToHook(this);
            // Wire up onExit/Retain/Enter state hooks
            fns.onExit = registerOnExitHook(this);
            fns.onRetain = registerOnRetainHook(this);
            fns.onEnter = registerOnEnterHook(this);
            // Wire up Resolve hooks
            fns.eagerResolve = registerEagerResolvePath(this);
            fns.lazyResolve = registerLazyResolveState(this);
            fns.resolveAll = registerResolveRemaining(this);
            // Wire up the View management hooks
            fns.loadViews = registerLoadEnteringViews(this);
            fns.activateViews = registerActivateViews(this);
            // Updates global state after a transition
            fns.updateGlobals = registerUpdateGlobalState(this);
            // After globals.current is updated at priority: 10000
            fns.updateUrl = registerUpdateUrl(this);
            // Lazy load state trees
            fns.lazyLoad = registerLazyLoadHook(this);
        };
        return TransitionService;
    }());

    /**
     * Provides services related to ui-router states.
     *
     * This API is located at `router.stateService` ([[UIRouter.stateService]])
     */
    var StateService = /** @class */ (function () {
        /** @internal */
        function StateService(/** @internal */ router) {
            this.router = router;
            /** @internal */
            this.invalidCallbacks = [];
            /** @internal */
            this._defaultErrorHandler = function $defaultErrorHandler($error$) {
                if ($error$ instanceof Error && $error$.stack) {
                    console.error($error$);
                    console.error($error$.stack);
                }
                else if ($error$ instanceof Rejection) {
                    console.error($error$.toString());
                    if ($error$.detail && $error$.detail.stack)
                        console.error($error$.detail.stack);
                }
                else {
                    console.error($error$);
                }
            };
            var getters = ['current', '$current', 'params', 'transition'];
            var boundFns = Object.keys(StateService.prototype).filter(not(inArray(getters)));
            createProxyFunctions(val(StateService.prototype), this, val(this), boundFns);
        }
        Object.defineProperty(StateService.prototype, "transition", {
            /**
             * The [[Transition]] currently in progress (or null)
             *
             * @deprecated This is a passthrough through to [[UIRouterGlobals.transition]]
             */
            get: function () {
                return this.router.globals.transition;
            },
            enumerable: false,
            configurable: true
        });
        Object.defineProperty(StateService.prototype, "params", {
            /**
             * The latest successful state parameters
             *
             * @deprecated This is a passthrough through to [[UIRouterGlobals.params]]
             */
            get: function () {
                return this.router.globals.params;
            },
            enumerable: false,
            configurable: true
        });
        Object.defineProperty(StateService.prototype, "current", {
            /**
             * The current [[StateDeclaration]]
             *
             * @deprecated This is a passthrough through to [[UIRouterGlobals.current]]
             */
            get: function () {
                return this.router.globals.current;
            },
            enumerable: false,
            configurable: true
        });
        Object.defineProperty(StateService.prototype, "$current", {
            /**
             * The current [[StateObject]] (an internal API)
             *
             * @deprecated This is a passthrough through to [[UIRouterGlobals.$current]]
             */
            get: function () {
                return this.router.globals.$current;
            },
            enumerable: false,
            configurable: true
        });
        /** @internal */
        StateService.prototype.dispose = function () {
            this.defaultErrorHandler(noop);
            this.invalidCallbacks = [];
        };
        /**
         * Handler for when [[transitionTo]] is called with an invalid state.
         *
         * Invokes the [[onInvalid]] callbacks, in natural order.
         * Each callback's return value is checked in sequence until one of them returns an instance of TargetState.
         * The results of the callbacks are wrapped in $q.when(), so the callbacks may return promises.
         *
         * If a callback returns an TargetState, then it is used as arguments to $state.transitionTo() and the result returned.
         *
         * @internal
         */
        StateService.prototype._handleInvalidTargetState = function (fromPath, toState) {
            var _this = this;
            var fromState = PathUtils.makeTargetState(this.router.stateRegistry, fromPath);
            var globals = this.router.globals;
            var latestThing = function () { return globals.transitionHistory.peekTail(); };
            var latest = latestThing();
            var callbackQueue = new Queue(this.invalidCallbacks.slice());
            var injector = new ResolveContext(fromPath).injector();
            var checkForRedirect = function (result) {
                if (!(result instanceof TargetState)) {
                    return;
                }
                var target = result;
                // Recreate the TargetState, in case the state is now defined.
                target = _this.target(target.identifier(), target.params(), target.options());
                if (!target.valid()) {
                    return Rejection.invalid(target.error()).toPromise();
                }
                if (latestThing() !== latest) {
                    return Rejection.superseded().toPromise();
                }
                return _this.transitionTo(target.identifier(), target.params(), target.options());
            };
            function invokeNextCallback() {
                var nextCallback = callbackQueue.dequeue();
                if (nextCallback === undefined)
                    return Rejection.invalid(toState.error()).toPromise();
                var callbackResult = services.$q.when(nextCallback(toState, fromState, injector));
                return callbackResult.then(checkForRedirect).then(function (result) { return result || invokeNextCallback(); });
            }
            return invokeNextCallback();
        };
        /**
         * Registers an Invalid State handler
         *
         * Registers a [[OnInvalidCallback]] function to be invoked when [[StateService.transitionTo]]
         * has been called with an invalid state reference parameter
         *
         * Example:
         * ```js
         * stateService.onInvalid(function(to, from, injector) {
         *   if (to.name() === 'foo') {
         *     let lazyLoader = injector.get('LazyLoadService');
         *     return lazyLoader.load('foo')
         *         .then(() => stateService.target('foo'));
         *   }
         * });
         * ```
         *
         * @param {function} callback invoked when the toState is invalid
         *   This function receives the (invalid) toState, the fromState, and an injector.
         *   The function may optionally return a [[TargetState]] or a Promise for a TargetState.
         *   If one is returned, it is treated as a redirect.
         *
         * @returns a function which deregisters the callback
         */
        StateService.prototype.onInvalid = function (callback) {
            this.invalidCallbacks.push(callback);
            return function deregisterListener() {
                removeFrom(this.invalidCallbacks)(callback);
            }.bind(this);
        };
        /**
         * Reloads the current state
         *
         * A method that force reloads the current state, or a partial state hierarchy.
         * All resolves are re-resolved, and components reinstantiated.
         *
         * #### Example:
         * ```js
         * let app angular.module('app', ['ui.router']);
         *
         * app.controller('ctrl', function ($scope, $state) {
         *   $scope.reload = function(){
         *     $state.reload();
         *   }
         * });
         * ```
         *
         * Note: `reload()` is just an alias for:
         *
         * ```js
         * $state.transitionTo($state.current, $state.params, {
         *   reload: true, inherit: false
         * });
         * ```
         *
         * @param reloadState A state name or a state object.
         *    If present, this state and all its children will be reloaded, but ancestors will not reload.
         *
         * #### Example:
         * ```js
         * //assuming app application consists of 3 states: 'contacts', 'contacts.detail', 'contacts.detail.item'
         * //and current state is 'contacts.detail.item'
         * let app angular.module('app', ['ui.router']);
         *
         * app.controller('ctrl', function ($scope, $state) {
         *   $scope.reload = function(){
         *     //will reload 'contact.detail' and nested 'contact.detail.item' states
         *     $state.reload('contact.detail');
         *   }
         * });
         * ```
         *
         * @returns A promise representing the state of the new transition. See [[StateService.go]]
         */
        StateService.prototype.reload = function (reloadState) {
            return this.transitionTo(this.current, this.params, {
                reload: isDefined(reloadState) ? reloadState : true,
                inherit: false,
                notify: false,
            });
        };
        /**
         * Transition to a different state and/or parameters
         *
         * Convenience method for transitioning to a new state.
         *
         * `$state.go` calls `$state.transitionTo` internally but automatically sets options to
         * `{ location: true, inherit: true, relative: router.globals.$current, notify: true }`.
         * This allows you to use either an absolute or relative `to` argument (because of `relative: router.globals.$current`).
         * It also allows you to specify * only the parameters you'd like to update, while letting unspecified parameters
         * inherit from the current parameter values (because of `inherit: true`).
         *
         * #### Example:
         * ```js
         * let app = angular.module('app', ['ui.router']);
         *
         * app.controller('ctrl', function ($scope, $state) {
         *   $scope.changeState = function () {
         *     $state.go('contact.detail');
         *   };
         * });
         * ```
         *
         * @param to Absolute state name, state object, or relative state path (relative to current state).
         *
         * Some examples:
         *
         * - `$state.go('contact.detail')` - will go to the `contact.detail` state
         * - `$state.go('^')` - will go to the parent state
         * - `$state.go('^.sibling')` - if current state is `home.child`, will go to the `home.sibling` state
         * - `$state.go('.child.grandchild')` - if current state is home, will go to the `home.child.grandchild` state
         *
         * @param params A map of the parameters that will be sent to the state, will populate $stateParams.
         *
         *    Any parameters that are not specified will be inherited from current parameter values (because of `inherit: true`).
         *    This allows, for example, going to a sibling state that shares parameters defined by a parent state.
         *
         * @param options Transition options
         *
         * @returns {promise} A promise representing the state of the new transition.
         */
        StateService.prototype.go = function (to, params, options) {
            var defautGoOpts = { relative: this.$current, inherit: true };
            var transOpts = defaults(options, defautGoOpts, defaultTransOpts);
            return this.transitionTo(to, params, transOpts);
        };
        /**
         * Creates a [[TargetState]]
         *
         * This is a factory method for creating a TargetState
         *
         * This may be returned from a Transition Hook to redirect a transition, for example.
         */
        StateService.prototype.target = function (identifier, params, options) {
            if (options === void 0) { options = {}; }
            // If we're reloading, find the state object to reload from
            if (isObject(options.reload) && !options.reload.name)
                throw new Error('Invalid reload state object');
            var reg = this.router.stateRegistry;
            options.reloadState =
                options.reload === true ? reg.root() : reg.matcher.find(options.reload, options.relative);
            if (options.reload && !options.reloadState)
                throw new Error("No such reload state '" + (isString(options.reload) ? options.reload : options.reload.name) + "'");
            return new TargetState(this.router.stateRegistry, identifier, params, options);
        };
        /** @internal */
        StateService.prototype.getCurrentPath = function () {
            var _this = this;
            var globals = this.router.globals;
            var latestSuccess = globals.successfulTransitions.peekTail();
            var rootPath = function () { return [new PathNode(_this.router.stateRegistry.root())]; };
            return latestSuccess ? latestSuccess.treeChanges().to : rootPath();
        };
        /**
         * Low-level method for transitioning to a new state.
         *
         * The [[go]] method (which uses `transitionTo` internally) is recommended in most situations.
         *
         * #### Example:
         * ```js
         * let app = angular.module('app', ['ui.router']);
         *
         * app.controller('ctrl', function ($scope, $state) {
         *   $scope.changeState = function () {
         *     $state.transitionTo('contact.detail');
         *   };
         * });
         * ```
         *
         * @param to State name or state object.
         * @param toParams A map of the parameters that will be sent to the state,
         *      will populate $stateParams.
         * @param options Transition options
         *
         * @returns A promise representing the state of the new transition. See [[go]]
         */
        StateService.prototype.transitionTo = function (to, toParams, options) {
            var _this = this;
            if (toParams === void 0) { toParams = {}; }
            if (options === void 0) { options = {}; }
            var router = this.router;
            var globals = router.globals;
            options = defaults(options, defaultTransOpts);
            var getCurrent = function () { return globals.transition; };
            options = extend(options, { current: getCurrent });
            var ref = this.target(to, toParams, options);
            var currentPath = this.getCurrentPath();
            if (!ref.exists())
                return this._handleInvalidTargetState(currentPath, ref);
            if (!ref.valid())
                return silentRejection(ref.error());
            if (options.supercede === false && getCurrent()) {
                return (Rejection.ignored('Another transition is in progress and supercede has been set to false in TransitionOptions for the transition. So the transition was ignored in favour of the existing one in progress.').toPromise());
            }
            /**
             * Special handling for Ignored, Aborted, and Redirected transitions
             *
             * The semantics for the transition.run() promise and the StateService.transitionTo()
             * promise differ. For instance, the run() promise may be rejected because it was
             * IGNORED, but the transitionTo() promise is resolved because from the user perspective
             * no error occurred.  Likewise, the transition.run() promise may be rejected because of
             * a Redirect, but the transitionTo() promise is chained to the new Transition's promise.
             */
            var rejectedTransitionHandler = function (trans) { return function (error) {
                if (error instanceof Rejection) {
                    var isLatest = router.globals.lastStartedTransitionId <= trans.$id;
                    if (error.type === exports.RejectType.IGNORED) {
                        isLatest && router.urlRouter.update();
                        // Consider ignored `Transition.run()` as a successful `transitionTo`
                        return services.$q.when(globals.current);
                    }
                    var detail = error.detail;
                    if (error.type === exports.RejectType.SUPERSEDED && error.redirected && detail instanceof TargetState) {
                        // If `Transition.run()` was redirected, allow the `transitionTo()` promise to resolve successfully
                        // by returning the promise for the new (redirect) `Transition.run()`.
                        var redirect = trans.redirect(detail);
                        return redirect.run().catch(rejectedTransitionHandler(redirect));
                    }
                    if (error.type === exports.RejectType.ABORTED) {
                        isLatest && router.urlRouter.update();
                        return services.$q.reject(error);
                    }
                }
                var errorHandler = _this.defaultErrorHandler();
                errorHandler(error);
                return services.$q.reject(error);
            }; };
            var transition = this.router.transitionService.create(currentPath, ref);
            var transitionToPromise = transition.run().catch(rejectedTransitionHandler(transition));
            silenceUncaughtInPromise(transitionToPromise); // issue #2676
            // Return a promise for the transition, which also has the transition object on it.
            return extend(transitionToPromise, { transition: transition });
        };
        /**
         * Checks if the current state *is* the provided state
         *
         * Similar to [[includes]] but only checks for the full state name.
         * If params is supplied then it will be tested for strict equality against the current
         * active params object, so all params must match with none missing and no extras.
         *
         * #### Example:
         * ```js
         * $state.$current.name = 'contacts.details.item';
         *
         * // absolute name
         * $state.is('contact.details.item'); // returns true
         * $state.is(contactDetailItemStateObject); // returns true
         * ```
         *
         * // relative name (. and ^), typically from a template
         * // E.g. from the 'contacts.details' template
         * ```html
         * <div ng-class="{highlighted: $state.is('.item')}">Item</div>
         * ```
         *
         * @param stateOrName The state name (absolute or relative) or state object you'd like to check.
         * @param params A param object, e.g. `{sectionId: section.id}`, that you'd like
         * to test against the current active state.
         * @param options An options object. The options are:
         *   - `relative`: If `stateOrName` is a relative state name and `options.relative` is set, .is will
         *     test relative to `options.relative` state (or name).
         *
         * @returns Returns true if it is the state.
         */
        StateService.prototype.is = function (stateOrName, params, options) {
            options = defaults(options, { relative: this.$current });
            var state = this.router.stateRegistry.matcher.find(stateOrName, options.relative);
            if (!isDefined(state))
                return undefined;
            if (this.$current !== state)
                return false;
            if (!params)
                return true;
            var schema = state.parameters({ inherit: true, matchingKeys: params });
            return Param.equals(schema, Param.values(schema, params), this.params);
        };
        /**
         * Checks if the current state *includes* the provided state
         *
         * A method to determine if the current active state is equal to or is the child of the
         * state stateName. If any params are passed then they will be tested for a match as well.
         * Not all the parameters need to be passed, just the ones you'd like to test for equality.
         *
         * #### Example when `$state.$current.name === 'contacts.details.item'`
         * ```js
         * // Using partial names
         * $state.includes("contacts"); // returns true
         * $state.includes("contacts.details"); // returns true
         * $state.includes("contacts.details.item"); // returns true
         * $state.includes("contacts.list"); // returns false
         * $state.includes("about"); // returns false
         * ```
         *
         * #### Glob Examples when `* $state.$current.name === 'contacts.details.item.url'`:
         * ```js
         * $state.includes("*.details.*.*"); // returns true
         * $state.includes("*.details.**"); // returns true
         * $state.includes("**.item.**"); // returns true
         * $state.includes("*.details.item.url"); // returns true
         * $state.includes("*.details.*.url"); // returns true
         * $state.includes("*.details.*"); // returns false
         * $state.includes("item.**"); // returns false
         * ```
         *
         * @param stateOrName A partial name, relative name, glob pattern,
         *   or state object to be searched for within the current state name.
         * @param params A param object, e.g. `{sectionId: section.id}`,
         *   that you'd like to test against the current active state.
         * @param options An options object. The options are:
         *   - `relative`: If `stateOrName` is a relative state name and `options.relative` is set, .is will
         *     test relative to `options.relative` state (or name).
         *
         * @returns {boolean} Returns true if it does include the state
         */
        StateService.prototype.includes = function (stateOrName, params, options) {
            options = defaults(options, { relative: this.$current });
            var glob = isString(stateOrName) && Glob.fromString(stateOrName);
            if (glob) {
                if (!glob.matches(this.$current.name))
                    return false;
                stateOrName = this.$current.name;
            }
            var state = this.router.stateRegistry.matcher.find(stateOrName, options.relative), include = this.$current.includes;
            if (!isDefined(state))
                return undefined;
            if (!isDefined(include[state.name]))
                return false;
            if (!params)
                return true;
            var schema = state.parameters({ inherit: true, matchingKeys: params });
            return Param.equals(schema, Param.values(schema, params), this.params);
        };
        /**
         * Generates a URL for a state and parameters
         *
         * Returns the url for the given state populated with the given params.
         *
         * #### Example:
         * ```js
         * expect($state.href("about.person", { person: "bob" })).toEqual("/about/bob");
         * ```
         *
         * @param stateOrName The state name or state object you'd like to generate a url from.
         * @param params An object of parameter values to fill the state's required parameters.
         * @param options Options object. The options are:
         *
         * @returns {string} compiled state url
         */
        StateService.prototype.href = function (stateOrName, params, options) {
            var defaultHrefOpts = {
                lossy: true,
                inherit: true,
                absolute: false,
                relative: this.$current,
            };
            options = defaults(options, defaultHrefOpts);
            params = params || {};
            var state = this.router.stateRegistry.matcher.find(stateOrName, options.relative);
            if (!isDefined(state))
                return null;
            if (options.inherit)
                params = this.params.$inherit(params, this.$current, state);
            var nav = state && options.lossy ? state.navigable : state;
            if (!nav || nav.url === undefined || nav.url === null) {
                return null;
            }
            return this.router.urlRouter.href(nav.url, params, { absolute: options.absolute });
        };
        /**
         * Sets or gets the default [[transitionTo]] error handler.
         *
         * The error handler is called when a [[Transition]] is rejected or when any error occurred during the Transition.
         * This includes errors caused by resolves and transition hooks.
         *
         * Note:
         * This handler does not receive certain Transition rejections.
         * Redirected and Ignored Transitions are not considered to be errors by [[StateService.transitionTo]].
         *
         * The built-in default error handler logs the error to the console.
         *
         * You can provide your own custom handler.
         *
         * #### Example:
         * ```js
         * stateService.defaultErrorHandler(function() {
         *   // Do not log transitionTo errors
         * });
         * ```
         *
         * @param handler a global error handler function
         * @returns the current global error handler
         */
        StateService.prototype.defaultErrorHandler = function (handler) {
            return (this._defaultErrorHandler = handler || this._defaultErrorHandler);
        };
        StateService.prototype.get = function (stateOrName, base) {
            var reg = this.router.stateRegistry;
            if (arguments.length === 0)
                return reg.get();
            return reg.get(stateOrName, base || this.$current);
        };
        /**
         * Lazy loads a state
         *
         * Explicitly runs a state's [[StateDeclaration.lazyLoad]] function.
         *
         * @param stateOrName the state that should be lazy loaded
         * @param transition the optional Transition context to use (if the lazyLoad function requires an injector, etc)
         * Note: If no transition is provided, a noop transition is created using the from the current state to the current state.
         * This noop transition is not actually run.
         *
         * @returns a promise to lazy load
         */
        StateService.prototype.lazyLoad = function (stateOrName, transition) {
            var state = this.get(stateOrName);
            if (!state || !state.lazyLoad)
                throw new Error('Can not lazy load ' + stateOrName);
            var currentPath = this.getCurrentPath();
            var target = PathUtils.makeTargetState(this.router.stateRegistry, currentPath);
            transition = transition || this.router.transitionService.create(currentPath, target);
            return lazyLoadState(transition, state);
        };
        return StateService;
    }());

    /**
     * An angular1-like promise api
     *
     * This object implements four methods similar to the
     * [angular 1 promise api](https://docs.angularjs.org/api/ng/service/$q)
     *
     * UI-Router evolved from an angular 1 library to a framework agnostic library.
     * However, some of the `@uirouter/core` code uses these ng1 style APIs to support ng1 style dependency injection.
     *
     * This API provides native ES6 promise support wrapped as a $q-like API.
     * Internally, UI-Router uses this $q object to perform promise operations.
     * The `angular-ui-router` (ui-router for angular 1) uses the $q API provided by angular.
     *
     * $q-like promise api
     */
    var $q = {
        /** Normalizes a value as a promise */
        when: function (val) { return new Promise(function (resolve, reject) { return resolve(val); }); },
        /** Normalizes a value as a promise rejection */
        reject: function (val) {
            return new Promise(function (resolve, reject) {
                reject(val);
            });
        },
        /** @returns a deferred object, which has `resolve` and `reject` functions */
        defer: function () {
            var deferred = {};
            deferred.promise = new Promise(function (resolve, reject) {
                deferred.resolve = resolve;
                deferred.reject = reject;
            });
            return deferred;
        },
        /** Like Promise.all(), but also supports object key/promise notation like $q */
        all: function (promises) {
            if (isArray(promises)) {
                return Promise.all(promises);
            }
            if (isObject(promises)) {
                // Convert promises map to promises array.
                // When each promise resolves, map it to a tuple { key: key, val: val }
                var chain = Object.keys(promises).map(function (key) { return promises[key].then(function (val) { return ({ key: key, val: val }); }); });
                // Then wait for all promises to resolve, and convert them back to an object
                return $q.all(chain).then(function (values) {
                    return values.reduce(function (acc, tuple) {
                        acc[tuple.key] = tuple.val;
                        return acc;
                    }, {});
                });
            }
        },
    };

    // globally available injectables
    var globals = {};
    var STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/gm;
    var ARGUMENT_NAMES = /([^\s,]+)/g;
    /**
     * A basic angular1-like injector api
     *
     * This object implements four methods similar to the
     * [angular 1 dependency injector](https://docs.angularjs.org/api/auto/service/$injector)
     *
     * UI-Router evolved from an angular 1 library to a framework agnostic library.
     * However, some of the `@uirouter/core` code uses these ng1 style APIs to support ng1 style dependency injection.
     *
     * This object provides a naive implementation of a globally scoped dependency injection system.
     * It supports the following DI approaches:
     *
     * ### Function parameter names
     *
     * A function's `.toString()` is called, and the parameter names are parsed.
     * This only works when the parameter names aren't "mangled" by a minifier such as UglifyJS.
     *
     * ```js
     * function injectedFunction(FooService, BarService) {
     *   // FooService and BarService are injected
     * }
     * ```
     *
     * ### Function annotation
     *
     * A function may be annotated with an array of dependency names as the `$inject` property.
     *
     * ```js
     * injectedFunction.$inject = [ 'FooService', 'BarService' ];
     * function injectedFunction(fs, bs) {
     *   // FooService and BarService are injected as fs and bs parameters
     * }
     * ```
     *
     * ### Array notation
     *
     * An array provides the names of the dependencies to inject (as strings).
     * The function is the last element of the array.
     *
     * ```js
     * [ 'FooService', 'BarService', function (fs, bs) {
     *   // FooService and BarService are injected as fs and bs parameters
     * }]
     * ```
     *
     * @type {$InjectorLike}
     */
    var $injector = {
        /** Gets an object from DI based on a string token */
        get: function (name) { return globals[name]; },
        /** Returns true if an object named `name` exists in global DI */
        has: function (name) { return $injector.get(name) != null; },
        /**
         * Injects a function
         *
         * @param fn the function to inject
         * @param context the function's `this` binding
         * @param locals An object with additional DI tokens and values, such as `{ someToken: { foo: 1 } }`
         */
        invoke: function (fn, context, locals) {
            var all = extend({}, globals, locals || {});
            var params = $injector.annotate(fn);
            var ensureExist = assertPredicate(function (key) { return all.hasOwnProperty(key); }, function (key) { return "DI can't find injectable: '" + key + "'"; });
            var args = params.filter(ensureExist).map(function (x) { return all[x]; });
            if (isFunction(fn))
                return fn.apply(context, args);
            else
                return fn.slice(-1)[0].apply(context, args);
        },
        /**
         * Returns a function's dependencies
         *
         * Analyzes a function (or array) and returns an array of DI tokens that the function requires.
         * @return an array of `string`s
         */
        annotate: function (fn) {
            if (!isInjectable(fn))
                throw new Error("Not an injectable function: " + fn);
            if (fn && fn.$inject)
                return fn.$inject;
            if (isArray(fn))
                return fn.slice(0, -1);
            var fnStr = fn.toString().replace(STRIP_COMMENTS, '');
            var result = fnStr.slice(fnStr.indexOf('(') + 1, fnStr.indexOf(')')).match(ARGUMENT_NAMES);
            return result || [];
        },
    };

    var keyValsToObjectR = function (accum, _a) {
        var key = _a[0], val = _a[1];
        if (!accum.hasOwnProperty(key)) {
            accum[key] = val;
        }
        else if (isArray(accum[key])) {
            accum[key].push(val);
        }
        else {
            accum[key] = [accum[key], val];
        }
        return accum;
    };
    var getParams = function (queryString) {
        return queryString.split('&').filter(identity).map(splitEqual).reduce(keyValsToObjectR, {});
    };
    function parseUrl$1(url) {
        var orEmptyString = function (x) { return x || ''; };
        var _a = splitHash(url).map(orEmptyString), beforehash = _a[0], hash = _a[1];
        var _b = splitQuery(beforehash).map(orEmptyString), path = _b[0], search = _b[1];
        return { path: path, search: search, hash: hash, url: url };
    }
    var buildUrl = function (loc) {
        var path = loc.path();
        var searchObject = loc.search();
        var hash = loc.hash();
        var search = Object.keys(searchObject)
            .map(function (key) {
            var param = searchObject[key];
            var vals = isArray(param) ? param : [param];
            return vals.map(function (val) { return key + '=' + val; });
        })
            .reduce(unnestR, [])
            .join('&');
        return path + (search ? '?' + search : '') + (hash ? '#' + hash : '');
    };
    function locationPluginFactory(name, isHtml5, serviceClass, configurationClass) {
        return function (uiRouter) {
            var service = (uiRouter.locationService = new serviceClass(uiRouter));
            var configuration = (uiRouter.locationConfig = new configurationClass(uiRouter, isHtml5));
            function dispose(router) {
                router.dispose(service);
                router.dispose(configuration);
            }
            return { name: name, service: service, configuration: configuration, dispose: dispose };
        };
    }

    /** A base `LocationServices` */
    var BaseLocationServices = /** @class */ (function () {
        function BaseLocationServices(router, fireAfterUpdate) {
            var _this = this;
            this.fireAfterUpdate = fireAfterUpdate;
            this._listeners = [];
            this._listener = function (evt) { return _this._listeners.forEach(function (cb) { return cb(evt); }); };
            this.hash = function () { return parseUrl$1(_this._get()).hash; };
            this.path = function () { return parseUrl$1(_this._get()).path; };
            this.search = function () { return getParams(parseUrl$1(_this._get()).search); };
            this._location = root.location;
            this._history = root.history;
        }
        BaseLocationServices.prototype.url = function (url, replace) {
            if (replace === void 0) { replace = true; }
            if (isDefined(url) && url !== this._get()) {
                this._set(null, null, url, replace);
                if (this.fireAfterUpdate) {
                    this._listeners.forEach(function (cb) { return cb({ url: url }); });
                }
            }
            return buildUrl(this);
        };
        BaseLocationServices.prototype.onChange = function (cb) {
            var _this = this;
            this._listeners.push(cb);
            return function () { return removeFrom(_this._listeners, cb); };
        };
        BaseLocationServices.prototype.dispose = function (router) {
            deregAll(this._listeners);
        };
        return BaseLocationServices;
    }());

    var __extends = (undefined && undefined.__extends) || (function () {
        var extendStatics = function (d, b) {
            extendStatics = Object.setPrototypeOf ||
                ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
                function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
            return extendStatics(d, b);
        };
        return function (d, b) {
            extendStatics(d, b);
            function __() { this.constructor = d; }
            d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
        };
    })();
    /** A `LocationServices` that uses the browser hash "#" to get/set the current location */
    var HashLocationService = /** @class */ (function (_super) {
        __extends(HashLocationService, _super);
        function HashLocationService(router) {
            var _this = _super.call(this, router, false) || this;
            root.addEventListener('hashchange', _this._listener, false);
            return _this;
        }
        HashLocationService.prototype._get = function () {
            return trimHashVal(this._location.hash);
        };
        HashLocationService.prototype._set = function (state, title, url, replace) {
            this._location.hash = url;
        };
        HashLocationService.prototype.dispose = function (router) {
            _super.prototype.dispose.call(this, router);
            root.removeEventListener('hashchange', this._listener);
        };
        return HashLocationService;
    }(BaseLocationServices));

    var __extends$1 = (undefined && undefined.__extends) || (function () {
        var extendStatics = function (d, b) {
            extendStatics = Object.setPrototypeOf ||
                ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
                function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
            return extendStatics(d, b);
        };
        return function (d, b) {
            extendStatics(d, b);
            function __() { this.constructor = d; }
            d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
        };
    })();
    /** A `LocationServices` that gets/sets the current location from an in-memory object */
    var MemoryLocationService = /** @class */ (function (_super) {
        __extends$1(MemoryLocationService, _super);
        function MemoryLocationService(router) {
            return _super.call(this, router, true) || this;
        }
        MemoryLocationService.prototype._get = function () {
            return this._url;
        };
        MemoryLocationService.prototype._set = function (state, title, url, replace) {
            this._url = url;
        };
        return MemoryLocationService;
    }(BaseLocationServices));

    var __extends$2 = (undefined && undefined.__extends) || (function () {
        var extendStatics = function (d, b) {
            extendStatics = Object.setPrototypeOf ||
                ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
                function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
            return extendStatics(d, b);
        };
        return function (d, b) {
            extendStatics(d, b);
            function __() { this.constructor = d; }
            d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
        };
    })();
    /**
     * A `LocationServices` that gets/sets the current location using the browser's `location` and `history` apis
     *
     * Uses `history.pushState` and `history.replaceState`
     */
    var PushStateLocationService = /** @class */ (function (_super) {
        __extends$2(PushStateLocationService, _super);
        function PushStateLocationService(router) {
            var _this = _super.call(this, router, true) || this;
            _this._config = router.urlService.config;
            root.addEventListener('popstate', _this._listener, false);
            return _this;
        }
        /**
         * Gets the base prefix without:
         * - trailing slash
         * - trailing filename
         * - protocol and hostname
         *
         * If <base href='/base/'>, this returns '/base'.
         * If <base href='/foo/base/'>, this returns '/foo/base'.
         * If <base href='/base/index.html'>, this returns '/base'.
         * If <base href='http://localhost:8080/base/index.html'>, this returns '/base'.
         * If <base href='/base'>, this returns ''.
         * If <base href='http://localhost:8080'>, this returns ''.
         * If <base href='http://localhost:8080/'>, this returns ''.
         *
         * See: https://html.spec.whatwg.org/dev/semantics.html#the-base-element
         */
        PushStateLocationService.prototype._getBasePrefix = function () {
            return stripLastPathElement(this._config.baseHref());
        };
        PushStateLocationService.prototype._get = function () {
            var _a = this._location, pathname = _a.pathname, hash = _a.hash, search = _a.search;
            search = splitQuery(search)[1]; // strip ? if found
            hash = splitHash(hash)[1]; // strip # if found
            var basePrefix = this._getBasePrefix();
            var exactBaseHrefMatch = pathname === this._config.baseHref();
            var startsWithBase = pathname.substr(0, basePrefix.length) === basePrefix;
            pathname = exactBaseHrefMatch ? '/' : startsWithBase ? pathname.substring(basePrefix.length) : pathname;
            return pathname + (search ? '?' + search : '') + (hash ? '#' + hash : '');
        };
        PushStateLocationService.prototype._set = function (state, title, url, replace) {
            var basePrefix = this._getBasePrefix();
            var slash = url && url[0] !== '/' ? '/' : '';
            var fullUrl = url === '' || url === '/' ? this._config.baseHref() : basePrefix + slash + url;
            if (replace) {
                this._history.replaceState(state, title, fullUrl);
            }
            else {
                this._history.pushState(state, title, fullUrl);
            }
        };
        PushStateLocationService.prototype.dispose = function (router) {
            _super.prototype.dispose.call(this, router);
            root.removeEventListener('popstate', this._listener);
        };
        return PushStateLocationService;
    }(BaseLocationServices));

    /** A `LocationConfig` mock that gets/sets all config from an in-memory object */
    var MemoryLocationConfig = /** @class */ (function () {
        function MemoryLocationConfig() {
            var _this = this;
            this.dispose = noop;
            this._baseHref = '';
            this._port = 80;
            this._protocol = 'http';
            this._host = 'localhost';
            this._hashPrefix = '';
            this.port = function () { return _this._port; };
            this.protocol = function () { return _this._protocol; };
            this.host = function () { return _this._host; };
            this.baseHref = function () { return _this._baseHref; };
            this.html5Mode = function () { return false; };
            this.hashPrefix = function (newval) { return (isDefined(newval) ? (_this._hashPrefix = newval) : _this._hashPrefix); };
        }
        return MemoryLocationConfig;
    }());

    /** A `LocationConfig` that delegates to the browser's `location` object */
    var BrowserLocationConfig = /** @class */ (function () {
        function BrowserLocationConfig(router, _isHtml5) {
            if (_isHtml5 === void 0) { _isHtml5 = false; }
            this._isHtml5 = _isHtml5;
            this._baseHref = undefined;
            this._hashPrefix = '';
        }
        BrowserLocationConfig.prototype.port = function () {
            if (location.port) {
                return Number(location.port);
            }
            return this.protocol() === 'https' ? 443 : 80;
        };
        BrowserLocationConfig.prototype.protocol = function () {
            return location.protocol.replace(/:/g, '');
        };
        BrowserLocationConfig.prototype.host = function () {
            return location.hostname;
        };
        BrowserLocationConfig.prototype.html5Mode = function () {
            return this._isHtml5;
        };
        BrowserLocationConfig.prototype.hashPrefix = function (newprefix) {
            return isDefined(newprefix) ? (this._hashPrefix = newprefix) : this._hashPrefix;
        };
        BrowserLocationConfig.prototype.baseHref = function (href) {
            if (isDefined(href))
                this._baseHref = href;
            if (isUndefined(this._baseHref))
                this._baseHref = this.getBaseHref();
            return this._baseHref;
        };
        BrowserLocationConfig.prototype.getBaseHref = function () {
            var baseTag = document.getElementsByTagName('base')[0];
            if (baseTag && baseTag.href) {
                return baseTag.href.replace(/^([^/:]*:)?\/\/[^/]*/, '');
            }
            return this._isHtml5 ? '/' : location.pathname || '/';
        };
        BrowserLocationConfig.prototype.dispose = function () { };
        return BrowserLocationConfig;
    }());

    function servicesPlugin(router) {
        services.$injector = $injector;
        services.$q = $q;
        return { name: 'vanilla.services', $q: $q, $injector: $injector, dispose: function () { return null; } };
    }
    /** A `UIRouterPlugin` uses the browser hash to get/set the current location */
    var hashLocationPlugin = locationPluginFactory('vanilla.hashBangLocation', false, HashLocationService, BrowserLocationConfig);
    /** A `UIRouterPlugin` that gets/sets the current location using the browser's `location` and `history` apis */
    var pushStateLocationPlugin = locationPluginFactory('vanilla.pushStateLocation', true, PushStateLocationService, BrowserLocationConfig);
    /** A `UIRouterPlugin` that gets/sets the current location from an in-memory object */
    var memoryLocationPlugin = locationPluginFactory('vanilla.memoryLocation', false, MemoryLocationService, MemoryLocationConfig);

    var UIRouterPluginBase = /** @class */ (function () {
        function UIRouterPluginBase() {
        }
        UIRouterPluginBase.prototype.dispose = function (router) { };
        return UIRouterPluginBase;
    }());

    var index = /*#__PURE__*/Object.freeze({
        __proto__: null,
        root: root,
        fromJson: fromJson,
        toJson: toJson,
        forEach: forEach,
        extend: extend,
        equals: equals,
        identity: identity,
        noop: noop,
        createProxyFunctions: createProxyFunctions,
        inherit: inherit,
        inArray: inArray,
        _inArray: _inArray,
        removeFrom: removeFrom,
        _removeFrom: _removeFrom,
        pushTo: pushTo,
        _pushTo: _pushTo,
        deregAll: deregAll,
        defaults: defaults,
        mergeR: mergeR,
        ancestors: ancestors,
        pick: pick,
        omit: omit,
        pluck: pluck,
        filter: filter,
        find: find,
        mapObj: mapObj,
        map: map,
        values: values,
        allTrueR: allTrueR,
        anyTrueR: anyTrueR,
        unnestR: unnestR,
        flattenR: flattenR,
        pushR: pushR,
        uniqR: uniqR,
        unnest: unnest,
        flatten: flatten,
        assertPredicate: assertPredicate,
        assertMap: assertMap,
        assertFn: assertFn,
        pairs: pairs,
        arrayTuples: arrayTuples,
        applyPairs: applyPairs,
        tail: tail,
        copy: copy,
        _extend: _extend,
        silenceUncaughtInPromise: silenceUncaughtInPromise,
        silentRejection: silentRejection,
        makeStub: makeStub,
        services: services,
        Glob: Glob,
        curry: curry,
        compose: compose,
        pipe: pipe,
        prop: prop,
        propEq: propEq,
        parse: parse,
        not: not,
        and: and,
        or: or,
        all: all,
        any: any,
        is: is,
        eq: eq,
        val: val,
        invoke: invoke,
        pattern: pattern,
        isUndefined: isUndefined,
        isDefined: isDefined,
        isNull: isNull,
        isNullOrUndefined: isNullOrUndefined,
        isFunction: isFunction,
        isNumber: isNumber,
        isString: isString,
        isObject: isObject,
        isArray: isArray,
        isDate: isDate,
        isRegExp: isRegExp,
        isInjectable: isInjectable,
        isPromise: isPromise,
        Queue: Queue,
        maxLength: maxLength,
        padString: padString,
        kebobString: kebobString,
        functionToString: functionToString,
        fnToString: fnToString,
        stringify: stringify,
        beforeAfterSubstr: beforeAfterSubstr,
        hostRegex: hostRegex,
        stripLastPathElement: stripLastPathElement,
        splitHash: splitHash,
        splitQuery: splitQuery,
        splitEqual: splitEqual,
        trimHashVal: trimHashVal,
        splitOnDelim: splitOnDelim,
        joinNeighborsR: joinNeighborsR,
        get Category () { return exports.Category; },
        Trace: Trace,
        trace: trace,
        get DefType () { return exports.DefType; },
        Param: Param,
        ParamTypes: ParamTypes,
        StateParams: StateParams,
        ParamType: ParamType,
        PathNode: PathNode,
        PathUtils: PathUtils,
        resolvePolicies: resolvePolicies,
        defaultResolvePolicy: defaultResolvePolicy,
        Resolvable: Resolvable,
        NATIVE_INJECTOR_TOKEN: NATIVE_INJECTOR_TOKEN,
        ResolveContext: ResolveContext,
        resolvablesBuilder: resolvablesBuilder,
        StateBuilder: StateBuilder,
        StateObject: StateObject,
        StateMatcher: StateMatcher,
        StateQueueManager: StateQueueManager,
        StateRegistry: StateRegistry,
        StateService: StateService,
        TargetState: TargetState,
        get TransitionHookPhase () { return exports.TransitionHookPhase; },
        get TransitionHookScope () { return exports.TransitionHookScope; },
        HookBuilder: HookBuilder,
        matchState: matchState,
        RegisteredHook: RegisteredHook,
        makeEvent: makeEvent,
        get RejectType () { return exports.RejectType; },
        Rejection: Rejection,
        Transition: Transition,
        TransitionHook: TransitionHook,
        TransitionEventType: TransitionEventType,
        defaultTransOpts: defaultTransOpts,
        TransitionService: TransitionService,
        UrlRules: UrlRules,
        UrlConfig: UrlConfig,
        UrlMatcher: UrlMatcher,
        ParamFactory: ParamFactory,
        UrlMatcherFactory: UrlMatcherFactory,
        UrlRouter: UrlRouter,
        UrlRuleFactory: UrlRuleFactory,
        BaseUrlRule: BaseUrlRule,
        UrlService: UrlService,
        ViewService: ViewService,
        UIRouterGlobals: UIRouterGlobals,
        UIRouter: UIRouter,
        $q: $q,
        $injector: $injector,
        BaseLocationServices: BaseLocationServices,
        HashLocationService: HashLocationService,
        MemoryLocationService: MemoryLocationService,
        PushStateLocationService: PushStateLocationService,
        MemoryLocationConfig: MemoryLocationConfig,
        BrowserLocationConfig: BrowserLocationConfig,
        keyValsToObjectR: keyValsToObjectR,
        getParams: getParams,
        parseUrl: parseUrl$1,
        buildUrl: buildUrl,
        locationPluginFactory: locationPluginFactory,
        servicesPlugin: servicesPlugin,
        hashLocationPlugin: hashLocationPlugin,
        pushStateLocationPlugin: pushStateLocationPlugin,
        memoryLocationPlugin: memoryLocationPlugin,
        UIRouterPluginBase: UIRouterPluginBase
    });

    /** @publicapi @module ng1 */ /** */
    /** @internalapi */
    function getNg1ViewConfigFactory() {
        var templateFactory = null;
        return function (path, view) {
            templateFactory = templateFactory || services.$injector.get('$templateFactory');
            return [new Ng1ViewConfig(path, view, templateFactory)];
        };
    }
    /** @internalapi */
    var hasAnyKey = function (keys, obj) { return keys.reduce(function (acc, key) { return acc || isDefined(obj[key]); }, false); };
    /**
     * This is a [[StateBuilder.builder]] function for angular1 `views`.
     *
     * When the [[StateBuilder]] builds a [[StateObject]] object from a raw [[StateDeclaration]], this builder
     * handles the `views` property with logic specific to @uirouter/angularjs (ng1).
     *
     * If no `views: {}` property exists on the [[StateDeclaration]], then it creates the `views` object
     * and applies the state-level configuration to a view named `$default`.
     *
     * @internalapi
     */
    function ng1ViewsBuilder(state) {
        // Do not process root state
        if (!state.parent)
            return {};
        var tplKeys = ['templateProvider', 'templateUrl', 'template', 'notify', 'async'], ctrlKeys = ['controller', 'controllerProvider', 'controllerAs', 'resolveAs'], compKeys = ['component', 'bindings', 'componentProvider'], nonCompKeys = tplKeys.concat(ctrlKeys), allViewKeys = compKeys.concat(nonCompKeys);
        // Do not allow a state to have both state-level props and also a `views: {}` property.
        // A state without a `views: {}` property can declare properties for the `$default` view as properties of the state.
        // However, the `$default` approach should not be mixed with a separate `views: ` block.
        if (isDefined(state.views) && hasAnyKey(allViewKeys, state)) {
            throw new Error("State '" + state.name + "' has a 'views' object. " +
                "It cannot also have \"view properties\" at the state level.  " +
                "Move the following properties into a view (in the 'views' object): " +
                (" " + allViewKeys.filter(function (key) { return isDefined(state[key]); }).join(', ')));
        }
        var views = {}, viewsObject = state.views || { $default: pick(state, allViewKeys) };
        forEach(viewsObject, function (config, name) {
            // Account for views: { "": { template... } }
            name = name || '$default';
            // Account for views: { header: "headerComponent" }
            if (isString(config))
                config = { component: config };
            // Make a shallow copy of the config object
            config = extend({}, config);
            // Do not allow a view to mix props for component-style view with props for template/controller-style view
            if (hasAnyKey(compKeys, config) && hasAnyKey(nonCompKeys, config)) {
                throw new Error("Cannot combine: " + compKeys.join('|') + " with: " + nonCompKeys.join('|') + " in stateview: '" + name + "@" + state.name + "'");
            }
            config.resolveAs = config.resolveAs || '$resolve';
            config.$type = 'ng1';
            config.$context = state;
            config.$name = name;
            var normalized = ViewService.normalizeUIViewTarget(config.$context, config.$name);
            config.$uiViewName = normalized.uiViewName;
            config.$uiViewContextAnchor = normalized.uiViewContextAnchor;
            views[name] = config;
        });
        return views;
    }
    /** @hidden */
    var id$1 = 0;
    /** @internalapi */
    var Ng1ViewConfig = /** @class */ (function () {
        function Ng1ViewConfig(path, viewDecl, factory) {
            var _this = this;
            this.path = path;
            this.viewDecl = viewDecl;
            this.factory = factory;
            this.$id = id$1++;
            this.loaded = false;
            this.getTemplate = function (uiView, context) {
                return _this.component
                    ? _this.factory.makeComponentTemplate(uiView, context, _this.component, _this.viewDecl.bindings)
                    : _this.template;
            };
        }
        Ng1ViewConfig.prototype.load = function () {
            var _this = this;
            var $q = services.$q;
            var context = new ResolveContext(this.path);
            var params = this.path.reduce(function (acc, node) { return extend(acc, node.paramValues); }, {});
            var promises = {
                template: $q.when(this.factory.fromConfig(this.viewDecl, params, context)),
                controller: $q.when(this.getController(context)),
            };
            return $q.all(promises).then(function (results) {
                trace.traceViewServiceEvent('Loaded', _this);
                _this.controller = results.controller;
                extend(_this, results.template); // Either { template: "tpl" } or { component: "cmpName" }
                return _this;
            });
        };
        /**
         * Gets the controller for a view configuration.
         *
         * @returns {Function|Promise.<Function>} Returns a controller, or a promise that resolves to a controller.
         */
        Ng1ViewConfig.prototype.getController = function (context) {
            var provider = this.viewDecl.controllerProvider;
            if (!isInjectable(provider))
                return this.viewDecl.controller;
            var deps = services.$injector.annotate(provider);
            var providerFn = isArray(provider) ? tail(provider) : provider;
            var resolvable = new Resolvable('', providerFn, deps);
            return resolvable.get(context);
        };
        return Ng1ViewConfig;
    }());

    /** @publicapi @module view */ /** */
    /**
     * Service which manages loading of templates from a ViewConfig.
     */
    var TemplateFactory = /** @class */ (function () {
        function TemplateFactory() {
            var _this = this;
            /** @hidden */ this._useHttp = ng.version.minor < 3;
            /** @hidden */ this.$get = [
                '$http',
                '$templateCache',
                '$injector',
                function ($http, $templateCache, $injector) {
                    _this.$templateRequest = $injector.has && $injector.has('$templateRequest') && $injector.get('$templateRequest');
                    _this.$http = $http;
                    _this.$templateCache = $templateCache;
                    return _this;
                },
            ];
        }
        /** @hidden */
        TemplateFactory.prototype.useHttpService = function (value) {
            this._useHttp = value;
        };
        /**
         * Creates a template from a configuration object.
         *
         * @param config Configuration object for which to load a template.
         * The following properties are search in the specified order, and the first one
         * that is defined is used to create the template:
         *
         * @param params  Parameters to pass to the template function.
         * @param context The resolve context associated with the template's view
         *
         * @return {string|object}  The template html as a string, or a promise for
         * that string,or `null` if no template is configured.
         */
        TemplateFactory.prototype.fromConfig = function (config, params, context) {
            var defaultTemplate = '<ui-view></ui-view>';
            var asTemplate = function (result) { return services.$q.when(result).then(function (str) { return ({ template: str }); }); };
            var asComponent = function (result) { return services.$q.when(result).then(function (str) { return ({ component: str }); }); };
            return isDefined(config.template)
                ? asTemplate(this.fromString(config.template, params))
                : isDefined(config.templateUrl)
                    ? asTemplate(this.fromUrl(config.templateUrl, params))
                    : isDefined(config.templateProvider)
                        ? asTemplate(this.fromProvider(config.templateProvider, params, context))
                        : isDefined(config.component)
                            ? asComponent(config.component)
                            : isDefined(config.componentProvider)
                                ? asComponent(this.fromComponentProvider(config.componentProvider, params, context))
                                : asTemplate(defaultTemplate);
        };
        /**
         * Creates a template from a string or a function returning a string.
         *
         * @param template html template as a string or function that returns an html template as a string.
         * @param params Parameters to pass to the template function.
         *
         * @return {string|object} The template html as a string, or a promise for that
         * string.
         */
        TemplateFactory.prototype.fromString = function (template, params) {
            return isFunction(template) ? template(params) : template;
        };
        /**
         * Loads a template from the a URL via `$http` and `$templateCache`.
         *
         * @param {string|Function} url url of the template to load, or a function
         * that returns a url.
         * @param {Object} params Parameters to pass to the url function.
         * @return {string|Promise.<string>} The template html as a string, or a promise
         * for that string.
         */
        TemplateFactory.prototype.fromUrl = function (url, params) {
            if (isFunction(url))
                url = url(params);
            if (url == null)
                return null;
            if (this._useHttp) {
                return this.$http
                    .get(url, { cache: this.$templateCache, headers: { Accept: 'text/html' } })
                    .then(function (response) {
                    return response.data;
                });
            }
            return this.$templateRequest(url);
        };
        /**
         * Creates a template by invoking an injectable provider function.
         *
         * @param provider Function to invoke via `locals`
         * @param {Function} injectFn a function used to invoke the template provider
         * @return {string|Promise.<string>} The template html as a string, or a promise
         * for that string.
         */
        TemplateFactory.prototype.fromProvider = function (provider, params, context) {
            var deps = services.$injector.annotate(provider);
            var providerFn = isArray(provider) ? tail(provider) : provider;
            var resolvable = new Resolvable('', providerFn, deps);
            return resolvable.get(context);
        };
        /**
         * Creates a component's template by invoking an injectable provider function.
         *
         * @param provider Function to invoke via `locals`
         * @param {Function} injectFn a function used to invoke the template provider
         * @return {string} The template html as a string: "<component-name input1='::$resolve.foo'></component-name>".
         */
        TemplateFactory.prototype.fromComponentProvider = function (provider, params, context) {
            var deps = services.$injector.annotate(provider);
            var providerFn = isArray(provider) ? tail(provider) : provider;
            var resolvable = new Resolvable('', providerFn, deps);
            return resolvable.get(context);
        };
        /**
         * Creates a template from a component's name
         *
         * This implements route-to-component.
         * It works by retrieving the component (directive) metadata from the injector.
         * It analyses the component's bindings, then constructs a template that instantiates the component.
         * The template wires input and output bindings to resolves or from the parent component.
         *
         * @param uiView {object} The parent ui-view (for binding outputs to callbacks)
         * @param context The ResolveContext (for binding outputs to callbacks returned from resolves)
         * @param component {string} Component's name in camel case.
         * @param bindings An object defining the component's bindings: {foo: '<'}
         * @return {string} The template as a string: "<component-name input1='::$resolve.foo'></component-name>".
         */
        TemplateFactory.prototype.makeComponentTemplate = function (uiView, context, component, bindings) {
            bindings = bindings || {};
            // Bind once prefix
            var prefix = ng.version.minor >= 3 ? '::' : '';
            // Convert to kebob name. Add x- prefix if the string starts with `x-` or `data-`
            var kebob = function (camelCase) {
                var kebobed = kebobString(camelCase);
                return /^(x|data)-/.exec(kebobed) ? "x-" + kebobed : kebobed;
            };
            var attributeTpl = function (input) {
                var name = input.name, type = input.type;
                var attrName = kebob(name);
                // If the ui-view has an attribute which matches a binding on the routed component
                // then pass that attribute through to the routed component template.
                // Prefer ui-view wired mappings to resolve data, unless the resolve was explicitly bound using `bindings:`
                if (uiView.attr(attrName) && !bindings[name])
                    return attrName + "='" + uiView.attr(attrName) + "'";
                var resolveName = bindings[name] || name;
                // Pre-evaluate the expression for "@" bindings by enclosing in {{ }}
                // some-attr="{{ ::$resolve.someResolveName }}"
                if (type === '@')
                    return attrName + "='{{" + prefix + "$resolve." + resolveName + "}}'";
                // Wire "&" callbacks to resolves that return a callback function
                // Get the result of the resolve (should be a function) and annotate it to get its arguments.
                // some-attr="$resolve.someResolveResultName(foo, bar)"
                if (type === '&') {
                    var res = context.getResolvable(resolveName);
                    var fn = res && res.data;
                    var args = (fn && services.$injector.annotate(fn)) || [];
                    // account for array style injection, i.e., ['foo', function(foo) {}]
                    var arrayIdxStr = isArray(fn) ? "[" + (fn.length - 1) + "]" : '';
                    return attrName + "='$resolve." + resolveName + arrayIdxStr + "(" + args.join(',') + ")'";
                }
                // some-attr="::$resolve.someResolveName"
                return attrName + "='" + prefix + "$resolve." + resolveName + "'";
            };
            var attrs = getComponentBindings(component).map(attributeTpl).join(' ');
            var kebobName = kebob(component);
            return "<" + kebobName + " " + attrs + "></" + kebobName + ">";
        };
        return TemplateFactory;
    }());
    // Gets all the directive(s)' inputs ('@', '=', and '<') and outputs ('&')
    function getComponentBindings(name) {
        var cmpDefs = services.$injector.get(name + 'Directive'); // could be multiple
        if (!cmpDefs || !cmpDefs.length)
            throw new Error("Unable to find component named '" + name + "'");
        return cmpDefs.map(getBindings).reduce(unnestR, []);
    }
    // Given a directive definition, find its object input attributes
    // Use different properties, depending on the type of directive (component, bindToController, normal)
    var getBindings = function (def) {
        if (isObject(def.bindToController))
            return scopeBindings(def.bindToController);
        return scopeBindings(def.scope);
    };
    // for ng 1.2 style, process the scope: { input: "=foo" }
    // for ng 1.3 through ng 1.5, process the component's bindToController: { input: "=foo" } object
    var scopeBindings = function (bindingsObj) {
        return Object.keys(bindingsObj || {})
            // [ 'input', [ '=foo', '=', 'foo' ] ]
            .map(function (key) { return [key, /^([=<@&])[?]?(.*)/.exec(bindingsObj[key])]; })
            // skip malformed values
            .filter(function (tuple) { return isDefined(tuple) && isArray(tuple[1]); })
            // { name: ('foo' || 'input'), type: '=' }
            .map(function (tuple) { return ({ name: tuple[1][2] || tuple[0], type: tuple[1][1] }); });
    };

    /** @publicapi @module ng1 */ /** */
    /**
     * The Angular 1 `StateProvider`
     *
     * The `$stateProvider` works similar to Angular's v1 router, but it focuses purely
     * on state.
     *
     * A state corresponds to a "place" in the application in terms of the overall UI and
     * navigation. A state describes (via the controller / template / view properties) what
     * the UI looks like and does at that place.
     *
     * States often have things in common, and the primary way of factoring out these
     * commonalities in this model is via the state hierarchy, i.e. parent/child states aka
     * nested states.
     *
     * The `$stateProvider` provides interfaces to declare these states for your app.
     */
    var StateProvider = /** @class */ (function () {
        function StateProvider(stateRegistry, stateService) {
            this.stateRegistry = stateRegistry;
            this.stateService = stateService;
            createProxyFunctions(val(StateProvider.prototype), this, val(this));
        }
        /**
         * Decorates states when they are registered
         *
         * Allows you to extend (carefully) or override (at your own peril) the
         * `stateBuilder` object used internally by [[StateRegistry]].
         * This can be used to add custom functionality to ui-router,
         * for example inferring templateUrl based on the state name.
         *
         * When passing only a name, it returns the current (original or decorated) builder
         * function that matches `name`.
         *
         * The builder functions that can be decorated are listed below. Though not all
         * necessarily have a good use case for decoration, that is up to you to decide.
         *
         * In addition, users can attach custom decorators, which will generate new
         * properties within the state's internal definition. There is currently no clear
         * use-case for this beyond accessing internal states (i.e. $state.$current),
         * however, expect this to become increasingly relevant as we introduce additional
         * meta-programming features.
         *
         * **Warning**: Decorators should not be interdependent because the order of
         * execution of the builder functions in non-deterministic. Builder functions
         * should only be dependent on the state definition object and super function.
         *
         *
         * Existing builder functions and current return values:
         *
         * - **parent** `{object}` - returns the parent state object.
         * - **data** `{object}` - returns state data, including any inherited data that is not
         *   overridden by own values (if any).
         * - **url** `{object}` - returns a {@link ui.router.util.type:UrlMatcher UrlMatcher}
         *   or `null`.
         * - **navigable** `{object}` - returns closest ancestor state that has a URL (aka is
         *   navigable).
         * - **params** `{object}` - returns an array of state params that are ensured to
         *   be a super-set of parent's params.
         * - **views** `{object}` - returns a views object where each key is an absolute view
         *   name (i.e. "viewName@stateName") and each value is the config object
         *   (template, controller) for the view. Even when you don't use the views object
         *   explicitly on a state config, one is still created for you internally.
         *   So by decorating this builder function you have access to decorating template
         *   and controller properties.
         * - **ownParams** `{object}` - returns an array of params that belong to the state,
         *   not including any params defined by ancestor states.
         * - **path** `{string}` - returns the full path from the root down to this state.
         *   Needed for state activation.
         * - **includes** `{object}` - returns an object that includes every state that
         *   would pass a `$state.includes()` test.
         *
         * #### Example:
         * Override the internal 'views' builder with a function that takes the state
         * definition, and a reference to the internal function being overridden:
         * ```js
         * $stateProvider.decorator('views', function (state, parent) {
         *   let result = {},
         *       views = parent(state);
         *
         *   angular.forEach(views, function (config, name) {
         *     let autoName = (state.name + '.' + name).replace('.', '/');
         *     config.templateUrl = config.templateUrl || '/partials/' + autoName + '.html';
         *     result[name] = config;
         *   });
         *   return result;
         * });
         *
         * $stateProvider.state('home', {
         *   views: {
         *     'contact.list': { controller: 'ListController' },
         *     'contact.item': { controller: 'ItemController' }
         *   }
         * });
         * ```
         *
         *
         * ```js
         * // Auto-populates list and item views with /partials/home/contact/list.html,
         * // and /partials/home/contact/item.html, respectively.
         * $state.go('home');
         * ```
         *
         * @param {string} name The name of the builder function to decorate.
         * @param {object} func A function that is responsible for decorating the original
         * builder function. The function receives two parameters:
         *
         *   - `{object}` - state - The state config object.
         *   - `{object}` - super - The original builder function.
         *
         * @return {object} $stateProvider - $stateProvider instance
         */
        StateProvider.prototype.decorator = function (name, func) {
            return this.stateRegistry.decorator(name, func) || this;
        };
        StateProvider.prototype.state = function (name, definition) {
            if (isObject(name)) {
                definition = name;
            }
            else {
                definition.name = name;
            }
            this.stateRegistry.register(definition);
            return this;
        };
        /**
         * Registers an invalid state handler
         *
         * This is a passthrough to [[StateService.onInvalid]] for ng1.
         */
        StateProvider.prototype.onInvalid = function (callback) {
            return this.stateService.onInvalid(callback);
        };
        return StateProvider;
    }());

    /** @publicapi @module ng1 */ /** */
    /**
     * This is a [[StateBuilder.builder]] function for angular1 `onEnter`, `onExit`,
     * `onRetain` callback hooks on a [[Ng1StateDeclaration]].
     *
     * When the [[StateBuilder]] builds a [[StateObject]] object from a raw [[StateDeclaration]], this builder
     * ensures that those hooks are injectable for @uirouter/angularjs (ng1).
     *
     * @internalapi
     */
    var getStateHookBuilder = function (hookName) {
        return function stateHookBuilder(stateObject) {
            var hook = stateObject[hookName];
            var pathname = hookName === 'onExit' ? 'from' : 'to';
            function decoratedNg1Hook(trans, state) {
                var resolveContext = new ResolveContext(trans.treeChanges(pathname));
                var subContext = resolveContext.subContext(state.$$state());
                var locals = extend(getLocals(subContext), { $state$: state, $transition$: trans });
                return services.$injector.invoke(hook, this, locals);
            }
            return hook ? decoratedNg1Hook : undefined;
        };
    };

    /** @publicapi @module ng1 */ /** */
    /**
     * Implements UI-Router LocationServices and LocationConfig using Angular 1's $location service
     * @internalapi
     */
    var Ng1LocationServices = /** @class */ (function () {
        function Ng1LocationServices($locationProvider) {
            // .onChange() registry
            this._urlListeners = [];
            this.$locationProvider = $locationProvider;
            var _lp = val($locationProvider);
            createProxyFunctions(_lp, this, _lp, ['hashPrefix']);
        }
        /**
         * Applys ng1-specific path parameter encoding
         *
         * The Angular 1 `$location` service is a bit weird.
         * It doesn't allow slashes to be encoded/decoded bi-directionally.
         *
         * See the writeup at https://github.com/angular-ui/ui-router/issues/2598
         *
         * This code patches the `path` parameter type so it encoded/decodes slashes as ~2F
         *
         * @param router
         */
        Ng1LocationServices.monkeyPatchPathParameterType = function (router) {
            var pathType = router.urlMatcherFactory.type('path');
            pathType.encode = function (x) {
                return x != null ? x.toString().replace(/(~|\/)/g, function (m) { return ({ '~': '~~', '/': '~2F' }[m]); }) : x;
            };
            pathType.decode = function (x) {
                return x != null ? x.toString().replace(/(~~|~2F)/g, function (m) { return ({ '~~': '~', '~2F': '/' }[m]); }) : x;
            };
        };
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        Ng1LocationServices.prototype.dispose = function () { };
        Ng1LocationServices.prototype.onChange = function (callback) {
            var _this = this;
            this._urlListeners.push(callback);
            return function () { return removeFrom(_this._urlListeners)(callback); };
        };
        Ng1LocationServices.prototype.html5Mode = function () {
            var html5Mode = this.$locationProvider.html5Mode();
            html5Mode = isObject(html5Mode) ? html5Mode.enabled : html5Mode;
            return html5Mode && this.$sniffer.history;
        };
        Ng1LocationServices.prototype.baseHref = function () {
            return this._baseHref || (this._baseHref = this.$browser.baseHref() || this.$window.location.pathname);
        };
        Ng1LocationServices.prototype.url = function (newUrl, replace, state) {
            if (replace === void 0) { replace = false; }
            if (isDefined(newUrl))
                this.$location.url(newUrl);
            if (replace)
                this.$location.replace();
            if (state)
                this.$location.state(state);
            return this.$location.url();
        };
        Ng1LocationServices.prototype._runtimeServices = function ($rootScope, $location, $sniffer, $browser, $window) {
            var _this = this;
            this.$location = $location;
            this.$sniffer = $sniffer;
            this.$browser = $browser;
            this.$window = $window;
            // Bind $locationChangeSuccess to the listeners registered in LocationService.onChange
            $rootScope.$on('$locationChangeSuccess', function (evt) { return _this._urlListeners.forEach(function (fn) { return fn(evt); }); });
            var _loc = val($location);
            // Bind these LocationService functions to $location
            createProxyFunctions(_loc, this, _loc, ['replace', 'path', 'search', 'hash']);
            // Bind these LocationConfig functions to $location
            createProxyFunctions(_loc, this, _loc, ['port', 'protocol', 'host']);
        };
        return Ng1LocationServices;
    }());

    /** @publicapi @module url */ /** */
    /**
     * Manages rules for client-side URL
     *
     * ### Deprecation warning:
     * This class is now considered to be an internal API
     * Use the [[UrlService]] instead.
     * For configuring URL rules, use the [[UrlRulesApi]] which can be found as [[UrlService.rules]].
     *
     * This class manages the router rules for what to do when the URL changes.
     *
     * This provider remains for backwards compatibility.
     *
     * @internalapi
     * @deprecated
     */
    var UrlRouterProvider = /** @class */ (function () {
        /** @hidden */
        function UrlRouterProvider(/** @hidden */ router) {
            this.router = router;
        }
        UrlRouterProvider.injectableHandler = function (router, handler) {
            return function (match) { return services.$injector.invoke(handler, null, { $match: match, $stateParams: router.globals.params }); };
        };
        /** @hidden */
        UrlRouterProvider.prototype.$get = function () {
            var urlService = this.router.urlService;
            this.router.urlRouter.update(true);
            if (!urlService.interceptDeferred)
                urlService.listen();
            return this.router.urlRouter;
        };
        /**
         * Registers a url handler function.
         *
         * Registers a low level url handler (a `rule`).
         * A rule detects specific URL patterns and returns a redirect, or performs some action.
         *
         * If a rule returns a string, the URL is replaced with the string, and all rules are fired again.
         *
         * #### Example:
         * ```js
         * var app = angular.module('app', ['ui.router.router']);
         *
         * app.config(function ($urlRouterProvider) {
         *   // Here's an example of how you might allow case insensitive urls
         *   $urlRouterProvider.rule(function ($injector, $location) {
         *     var path = $location.path(),
         *         normalized = path.toLowerCase();
         *
         *     if (path !== normalized) {
         *       return normalized;
         *     }
         *   });
         * });
         * ```
         *
         * @param ruleFn
         * Handler function that takes `$injector` and `$location` services as arguments.
         * You can use them to detect a url and return a different url as a string.
         *
         * @return [[UrlRouterProvider]] (`this`)
         */
        UrlRouterProvider.prototype.rule = function (ruleFn) {
            var _this = this;
            if (!isFunction(ruleFn))
                throw new Error("'rule' must be a function");
            var match = function () { return ruleFn(services.$injector, _this.router.locationService); };
            var rule = new BaseUrlRule(match, identity);
            this.router.urlService.rules.rule(rule);
            return this;
        };
        /**
         * Defines the path or behavior to use when no url can be matched.
         *
         * #### Example:
         * ```js
         * var app = angular.module('app', ['ui.router.router']);
         *
         * app.config(function ($urlRouterProvider) {
         *   // if the path doesn't match any of the urls you configured
         *   // otherwise will take care of routing the user to the
         *   // specified url
         *   $urlRouterProvider.otherwise('/index');
         *
         *   // Example of using function rule as param
         *   $urlRouterProvider.otherwise(function ($injector, $location) {
         *     return '/a/valid/url';
         *   });
         * });
         * ```
         *
         * @param rule
         * The url path you want to redirect to or a function rule that returns the url path or performs a `$state.go()`.
         * The function version is passed two params: `$injector` and `$location` services, and should return a url string.
         *
         * @return {object} `$urlRouterProvider` - `$urlRouterProvider` instance
         */
        UrlRouterProvider.prototype.otherwise = function (rule) {
            var _this = this;
            var urlRules = this.router.urlService.rules;
            if (isString(rule)) {
                urlRules.otherwise(rule);
            }
            else if (isFunction(rule)) {
                urlRules.otherwise(function () { return rule(services.$injector, _this.router.locationService); });
            }
            else {
                throw new Error("'rule' must be a string or function");
            }
            return this;
        };
        /**
         * Registers a handler for a given url matching.
         *
         * If the handler is a string, it is
         * treated as a redirect, and is interpolated according to the syntax of match
         * (i.e. like `String.replace()` for `RegExp`, or like a `UrlMatcher` pattern otherwise).
         *
         * If the handler is a function, it is injectable.
         * It gets invoked if `$location` matches.
         * You have the option of inject the match object as `$match`.
         *
         * The handler can return
         *
         * - **falsy** to indicate that the rule didn't match after all, then `$urlRouter`
         *   will continue trying to find another one that matches.
         * - **string** which is treated as a redirect and passed to `$location.url()`
         * - **void** or any **truthy** value tells `$urlRouter` that the url was handled.
         *
         * #### Example:
         * ```js
         * var app = angular.module('app', ['ui.router.router']);
         *
         * app.config(function ($urlRouterProvider) {
         *   $urlRouterProvider.when($state.url, function ($match, $stateParams) {
         *     if ($state.$current.navigable !== state ||
         *         !equalForKeys($match, $stateParams) {
         *      $state.transitionTo(state, $match, false);
         *     }
         *   });
         * });
         * ```
         *
         * @param what A pattern string to match, compiled as a [[UrlMatcher]].
         * @param handler The path (or function that returns a path) that you want to redirect your user to.
         * @param ruleCallback [optional] A callback that receives the `rule` registered with [[UrlMatcher.rule]]
         *
         * Note: the handler may also invoke arbitrary code, such as `$state.go()`
         */
        UrlRouterProvider.prototype.when = function (what, handler) {
            if (isArray(handler) || isFunction(handler)) {
                handler = UrlRouterProvider.injectableHandler(this.router, handler);
            }
            this.router.urlService.rules.when(what, handler);
            return this;
        };
        /**
         * Disables monitoring of the URL.
         *
         * Call this method before UI-Router has bootstrapped.
         * It will stop UI-Router from performing the initial url sync.
         *
         * This can be useful to perform some asynchronous initialization before the router starts.
         * Once the initialization is complete, call [[listen]] to tell UI-Router to start watching and synchronizing the URL.
         *
         * #### Example:
         * ```js
         * var app = angular.module('app', ['ui.router']);
         *
         * app.config(function ($urlRouterProvider) {
         *   // Prevent $urlRouter from automatically intercepting URL changes;
         *   $urlRouterProvider.deferIntercept();
         * })
         *
         * app.run(function (MyService, $urlRouter, $http) {
         *   $http.get("/stuff").then(function(resp) {
         *     MyService.doStuff(resp.data);
         *     $urlRouter.listen();
         *     $urlRouter.sync();
         *   });
         * });
         * ```
         *
         * @param defer Indicates whether to defer location change interception.
         *        Passing no parameter is equivalent to `true`.
         */
        UrlRouterProvider.prototype.deferIntercept = function (defer) {
            this.router.urlService.deferIntercept(defer);
        };
        return UrlRouterProvider;
    }());

    /* eslint-disable @typescript-eslint/no-empty-function */
    ng.module('ui.router.angular1', []);
    var mod_init = ng.module('ui.router.init', ['ng']);
    var mod_util = ng.module('ui.router.util', ['ui.router.init']);
    var mod_rtr = ng.module('ui.router.router', ['ui.router.util']);
    var mod_state = ng.module('ui.router.state', ['ui.router.router', 'ui.router.util', 'ui.router.angular1']);
    var mod_main = ng.module('ui.router', ['ui.router.init', 'ui.router.state', 'ui.router.angular1']);
    var mod_cmpt = ng.module('ui.router.compat', ['ui.router']);
    var router = null;
    $uiRouterProvider.$inject = ['$locationProvider'];
    /** This angular 1 provider instantiates a Router and exposes its services via the angular injector */
    function $uiRouterProvider($locationProvider) {
        // Create a new instance of the Router when the $uiRouterProvider is initialized
        router = this.router = new UIRouter();
        router.stateProvider = new StateProvider(router.stateRegistry, router.stateService);
        // Apply ng1 specific StateBuilder code for `views`, `resolve`, and `onExit/Retain/Enter` properties
        router.stateRegistry.decorator('views', ng1ViewsBuilder);
        router.stateRegistry.decorator('onExit', getStateHookBuilder('onExit'));
        router.stateRegistry.decorator('onRetain', getStateHookBuilder('onRetain'));
        router.stateRegistry.decorator('onEnter', getStateHookBuilder('onEnter'));
        router.viewService._pluginapi._viewConfigFactory('ng1', getNg1ViewConfigFactory());
        // Disable decoding of params by UrlMatcherFactory because $location already handles this
        router.urlService.config._decodeParams = false;
        var ng1LocationService = (router.locationService = router.locationConfig = new Ng1LocationServices($locationProvider));
        Ng1LocationServices.monkeyPatchPathParameterType(router);
        // backwards compat: also expose router instance as $uiRouterProvider.router
        router['router'] = router;
        router['$get'] = $get;
        $get.$inject = ['$location', '$browser', '$window', '$sniffer', '$rootScope', '$http', '$templateCache'];
        function $get($location, $browser, $window, $sniffer, $rootScope, $http, $templateCache) {
            ng1LocationService._runtimeServices($rootScope, $location, $sniffer, $browser, $window);
            delete router['router'];
            delete router['$get'];
            return router;
        }
        return router;
    }
    var getProviderFor = function (serviceName) { return [
        '$uiRouterProvider',
        function ($urp) {
            var service = $urp.router[serviceName];
            service['$get'] = function () { return service; };
            return service;
        },
    ]; };
    // This effectively calls $get() on `$uiRouterProvider` to trigger init (when ng enters runtime)
    runBlock.$inject = ['$injector', '$q', '$uiRouter'];
    function runBlock($injector, $q, $uiRouter) {
        services.$injector = $injector;
        services.$q = $q;
        // https://github.com/angular-ui/ui-router/issues/3678
        if (!Object.prototype.hasOwnProperty.call($injector, 'strictDi')) {
            try {
                $injector.invoke(function (checkStrictDi) { });
            }
            catch (error) {
                $injector.strictDi = !!/strict mode/.exec(error && error.toString());
            }
        }
        // The $injector is now available.
        // Find any resolvables that had dependency annotation deferred
        $uiRouter.stateRegistry
            .get()
            .map(function (x) { return x.$$state().resolvables; })
            .reduce(unnestR, [])
            .filter(function (x) { return x.deps === 'deferred'; })
            .forEach(function (resolvable) { return (resolvable.deps = $injector.annotate(resolvable.resolveFn, $injector.strictDi)); });
    }
    // $urlRouter service and $urlRouterProvider
    var getUrlRouterProvider = function (uiRouter) { return (uiRouter.urlRouterProvider = new UrlRouterProvider(uiRouter)); };
    // $state service and $stateProvider
    // $urlRouter service and $urlRouterProvider
    var getStateProvider = function () { return extend(router.stateProvider, { $get: function () { return router.stateService; } }); };
    watchDigests.$inject = ['$rootScope'];
    function watchDigests($rootScope) {
        $rootScope.$watch(function () {
            trace.approximateDigests++;
        });
    }
    mod_init.provider('$uiRouter', $uiRouterProvider);
    mod_rtr.provider('$urlRouter', ['$uiRouterProvider', getUrlRouterProvider]);
    mod_util.provider('$urlService', getProviderFor('urlService'));
    mod_util.provider('$urlMatcherFactory', ['$uiRouterProvider', function () { return router.urlMatcherFactory; }]);
    mod_util.provider('$templateFactory', function () { return new TemplateFactory(); });
    mod_state.provider('$stateRegistry', getProviderFor('stateRegistry'));
    mod_state.provider('$uiRouterGlobals', getProviderFor('globals'));
    mod_state.provider('$transitions', getProviderFor('transitionService'));
    mod_state.provider('$state', ['$uiRouterProvider', getStateProvider]);
    mod_state.factory('$stateParams', ['$uiRouter', function ($uiRouter) { return $uiRouter.globals.params; }]);
    mod_main.factory('$view', function () { return router.viewService; });
    mod_main.service('$trace', function () { return trace; });
    mod_main.run(watchDigests);
    mod_util.run(['$urlMatcherFactory', function ($urlMatcherFactory) { }]);
    mod_state.run(['$state', function ($state) { }]);
    mod_rtr.run(['$urlRouter', function ($urlRouter) { }]);
    mod_init.run(runBlock);
    /** @hidden TODO: find a place to move this */
    var getLocals = function (ctx) {
        var tokens = ctx.getTokens().filter(isString);
        var tuples = tokens.map(function (key) {
            var resolvable = ctx.getResolvable(key);
            var waitPolicy = ctx.getPolicy(resolvable).async;
            return [key, waitPolicy === 'NOWAIT' ? resolvable.promise : resolvable.data];
        });
        return tuples.reduce(applyPairs, {});
    };

    /* eslint-disable @typescript-eslint/no-empty-interface */
    /** @hidden */
    function parseStateRef(ref) {
        var paramsOnly = ref.match(/^\s*({[^}]*})\s*$/);
        if (paramsOnly)
            ref = '(' + paramsOnly[1] + ')';
        var parsed = ref.replace(/\n/g, ' ').match(/^\s*([^(]*?)\s*(\((.*)\))?\s*$/);
        if (!parsed || parsed.length !== 4)
            throw new Error("Invalid state ref '" + ref + "'");
        return { state: parsed[1] || null, paramExpr: parsed[3] || null };
    }
    /** @hidden */
    function stateContext(el) {
        var $uiView = el.parent().inheritedData('$uiView');
        var path = parse('$cfg.path')($uiView);
        return path ? tail(path).state.name : undefined;
    }
    /** @hidden */
    function processedDef($state, $element, def) {
        var uiState = def.uiState || $state.current.name;
        var uiStateOpts = extend(defaultOpts($element, $state), def.uiStateOpts || {});
        var href = $state.href(uiState, def.uiStateParams, uiStateOpts);
        return { uiState: uiState, uiStateParams: def.uiStateParams, uiStateOpts: uiStateOpts, href: href };
    }
    /** @hidden */
    function getTypeInfo(el) {
        // SVGAElement does not use the href attribute, but rather the 'xlinkHref' attribute.
        var isSvg = Object.prototype.toString.call(el.prop('href')) === '[object SVGAnimatedString]';
        var isForm = el[0].nodeName === 'FORM';
        return {
            attr: isForm ? 'action' : isSvg ? 'xlink:href' : 'href',
            isAnchor: el.prop('tagName').toUpperCase() === 'A',
            clickable: !isForm,
        };
    }
    /** @hidden */
    function clickHook(el, $state, $timeout, type, getDef) {
        return function (e) {
            var button = e.which || e.button, target = getDef();
            if (!(button > 1 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || el.attr('target'))) {
                // HACK: This is to allow ng-clicks to be processed before the transition is initiated:
                var transition_1 = $timeout(function () {
                    if (!el.attr('disabled')) {
                        $state.go(target.uiState, target.uiStateParams, target.uiStateOpts);
                    }
                });
                e.preventDefault();
                // if the state has no URL, ignore one preventDefault from the <a> directive.
                var ignorePreventDefaultCount_1 = type.isAnchor && !target.href ? 1 : 0;
                e.preventDefault = function () {
                    if (ignorePreventDefaultCount_1-- <= 0)
                        $timeout.cancel(transition_1);
                };
            }
        };
    }
    /** @hidden */
    function defaultOpts(el, $state) {
        return {
            relative: stateContext(el) || $state.$current,
            inherit: true,
            source: 'sref',
        };
    }
    /** @hidden */
    function bindEvents(element, scope, hookFn, uiStateOpts) {
        var events;
        if (uiStateOpts) {
            events = uiStateOpts.events;
        }
        if (!isArray(events)) {
            events = ['click'];
        }
        var on = element.on ? 'on' : 'bind';
        for (var _i = 0, events_1 = events; _i < events_1.length; _i++) {
            var event_1 = events_1[_i];
            element[on](event_1, hookFn);
        }
        scope.$on('$destroy', function () {
            var off = element.off ? 'off' : 'unbind';
            for (var _i = 0, events_2 = events; _i < events_2.length; _i++) {
                var event_2 = events_2[_i];
                element[off](event_2, hookFn);
            }
        });
    }
    /**
     * `ui-sref`: A directive for linking to a state
     *
     * A directive which links to a state (and optionally, parameters).
     * When clicked, this directive activates the linked state with the supplied parameter values.
     *
     * ### Linked State
     * The attribute value of the `ui-sref` is the name of the state to link to.
     *
     * #### Example:
     * This will activate the `home` state when the link is clicked.
     * ```html
     * <a ui-sref="home">Home</a>
     * ```
     *
     * ### Relative Links
     * You can also use relative state paths within `ui-sref`, just like a relative path passed to `$state.go()` ([[StateService.go]]).
     * You just need to be aware that the path is relative to the state that *created* the link.
     * This allows a state to create a relative `ui-sref` which always targets the same destination.
     *
     * #### Example:
     * Both these links are relative to the parent state, even when a child state is currently active.
     * ```html
     * <a ui-sref=".child1">child 1 state</a>
     * <a ui-sref=".child2">child 2 state</a>
     * ```
     *
     * This link activates the parent state.
     * ```html
     * <a ui-sref="^">Return</a>
     * ```
     *
     * ### hrefs
     * If the linked state has a URL, the directive will automatically generate and
     * update the `href` attribute (using the [[StateService.href]]  method).
     *
     * #### Example:
     * Assuming the `users` state has a url of `/users/`
     * ```html
     * <a ui-sref="users" href="/users/">Users</a>
     * ```
     *
     * ### Parameter Values
     * In addition to the state name, a `ui-sref` can include parameter values which are applied when activating the state.
     * Param values can be provided in the `ui-sref` value after the state name, enclosed by parentheses.
     * The content inside the parentheses is an expression, evaluated to the parameter values.
     *
     * #### Example:
     * This example renders a list of links to users.
     * The state's `userId` parameter value comes from each user's `user.id` property.
     * ```html
     * <li ng-repeat="user in users">
     *   <a ui-sref="users.detail({ userId: user.id })">{{ user.displayName }}</a>
     * </li>
     * ```
     *
     * Note:
     * The parameter values expression is `$watch`ed for updates.
     *
     * ### Transition Options
     * You can specify [[TransitionOptions]] to pass to [[StateService.go]] by using the `ui-sref-opts` attribute.
     * Options are restricted to `location`, `inherit`, and `reload`.
     *
     * #### Example:
     * ```html
     * <a ui-sref="home" ui-sref-opts="{ reload: true }">Home</a>
     * ```
     *
     * ### Other DOM Events
     *
     * You can also customize which DOM events to respond to (instead of `click`) by
     * providing an `events` array in the `ui-sref-opts` attribute.
     *
     * #### Example:
     * ```html
     * <input type="text" ui-sref="contacts" ui-sref-opts="{ events: ['change', 'blur'] }">
     * ```
     *
     * ### Highlighting the active link
     * This directive can be used in conjunction with [[uiSrefActive]] to highlight the active link.
     *
     * ### Examples
     * If you have the following template:
     *
     * ```html
     * <a ui-sref="home">Home</a>
     * <a ui-sref="about">About</a>
     * <a ui-sref="{page: 2}">Next page</a>
     *
     * <ul>
     *     <li ng-repeat="contact in contacts">
     *         <a ui-sref="contacts.detail({ id: contact.id })">{{ contact.name }}</a>
     *     </li>
     * </ul>
     * ```
     *
     * Then (assuming the current state is `contacts`) the rendered html including hrefs would be:
     *
     * ```html
     * <a href="#/home" ui-sref="home">Home</a>
     * <a href="#/about" ui-sref="about">About</a>
     * <a href="#/contacts?page=2" ui-sref="{page: 2}">Next page</a>
     *
     * <ul>
     *     <li ng-repeat="contact in contacts">
     *         <a href="#/contacts/1" ui-sref="contacts.detail({ id: contact.id })">Joe</a>
     *     </li>
     *     <li ng-repeat="contact in contacts">
     *         <a href="#/contacts/2" ui-sref="contacts.detail({ id: contact.id })">Alice</a>
     *     </li>
     *     <li ng-repeat="contact in contacts">
     *         <a href="#/contacts/3" ui-sref="contacts.detail({ id: contact.id })">Bob</a>
     *     </li>
     * </ul>
     *
     * <a href="#/home" ui-sref="home" ui-sref-opts="{reload: true}">Home</a>
     * ```
     *
     * ### Notes
     *
     * - You can use `ui-sref` to change **only the parameter values** by omitting the state name and parentheses.
     * #### Example:
     * Sets the `lang` parameter to `en` and remains on the same state.
     *
     * ```html
     * <a ui-sref="{ lang: 'en' }">English</a>
     * ```
     *
     * - A middle-click, right-click, or ctrl-click is handled (natively) by the browser to open the href in a new window, for example.
     *
     * - Unlike the parameter values expression, the state name is not `$watch`ed (for performance reasons).
     * If you need to dynamically update the state being linked to, use the fully dynamic [[uiState]] directive.
     */
    var uiSrefDirective;
    uiSrefDirective = [
        '$uiRouter',
        '$timeout',
        function $StateRefDirective($uiRouter, $timeout) {
            var $state = $uiRouter.stateService;
            return {
                restrict: 'A',
                require: ['?^uiSrefActive', '?^uiSrefActiveEq'],
                link: function (scope, element, attrs, uiSrefActive) {
                    var type = getTypeInfo(element);
                    var active = uiSrefActive[1] || uiSrefActive[0];
                    var unlinkInfoFn = null;
                    var rawDef = {};
                    var getDef = function () { return processedDef($state, element, rawDef); };
                    var ref = parseStateRef(attrs.uiSref);
                    rawDef.uiState = ref.state;
                    rawDef.uiStateOpts = attrs.uiSrefOpts ? scope.$eval(attrs.uiSrefOpts) : {};
                    function update() {
                        var def = getDef();
                        if (unlinkInfoFn)
                            unlinkInfoFn();
                        if (active)
                            unlinkInfoFn = active.$$addStateInfo(def.uiState, def.uiStateParams);
                        if (def.href != null)
                            attrs.$set(type.attr, def.href);
                    }
                    if (ref.paramExpr) {
                        scope.$watch(ref.paramExpr, function (val) {
                            rawDef.uiStateParams = extend({}, val);
                            update();
                        }, true);
                        rawDef.uiStateParams = extend({}, scope.$eval(ref.paramExpr));
                    }
                    update();
                    scope.$on('$destroy', $uiRouter.stateRegistry.onStatesChanged(update));
                    scope.$on('$destroy', $uiRouter.transitionService.onSuccess({}, update));
                    if (!type.clickable)
                        return;
                    var hookFn = clickHook(element, $state, $timeout, type, getDef);
                    bindEvents(element, scope, hookFn, rawDef.uiStateOpts);
                },
            };
        },
    ];
    /**
     * `ui-state`: A fully dynamic directive for linking to a state
     *
     * A directive which links to a state (and optionally, parameters).
     * When clicked, this directive activates the linked state with the supplied parameter values.
     *
     * **This directive is very similar to [[uiSref]], but it `$observe`s and `$watch`es/evaluates all its inputs.**
     *
     * A directive which links to a state (and optionally, parameters).
     * When clicked, this directive activates the linked state with the supplied parameter values.
     *
     * ### Linked State
     * The attribute value of `ui-state` is an expression which is `$watch`ed and evaluated as the state to link to.
     * **This is in contrast with `ui-sref`, which takes a state name as a string literal.**
     *
     * #### Example:
     * Create a list of links.
     * ```html
     * <li ng-repeat="link in navlinks">
     *   <a ui-state="link.state">{{ link.displayName }}</a>
     * </li>
     * ```
     *
     * ### Relative Links
     * If the expression evaluates to a relative path, it is processed like [[uiSref]].
     * You just need to be aware that the path is relative to the state that *created* the link.
     * This allows a state to create relative `ui-state` which always targets the same destination.
     *
     * ### hrefs
     * If the linked state has a URL, the directive will automatically generate and
     * update the `href` attribute (using the [[StateService.href]]  method).
     *
     * ### Parameter Values
     * In addition to the state name expression, a `ui-state` can include parameter values which are applied when activating the state.
     * Param values should be provided using the `ui-state-params` attribute.
     * The `ui-state-params` attribute value is `$watch`ed and evaluated as an expression.
     *
     * #### Example:
     * This example renders a list of links with param values.
     * The state's `userId` parameter value comes from each user's `user.id` property.
     * ```html
     * <li ng-repeat="link in navlinks">
     *   <a ui-state="link.state" ui-state-params="link.params">{{ link.displayName }}</a>
     * </li>
     * ```
     *
     * ### Transition Options
     * You can specify [[TransitionOptions]] to pass to [[StateService.go]] by using the `ui-state-opts` attribute.
     * Options are restricted to `location`, `inherit`, and `reload`.
     * The value of the `ui-state-opts` is `$watch`ed and evaluated as an expression.
     *
     * #### Example:
     * ```html
     * <a ui-state="returnto.state" ui-state-opts="{ reload: true }">Home</a>
     * ```
     *
     * ### Other DOM Events
     *
     * You can also customize which DOM events to respond to (instead of `click`) by
     * providing an `events` array in the `ui-state-opts` attribute.
     *
     * #### Example:
     * ```html
     * <input type="text" ui-state="contacts" ui-state-opts="{ events: ['change', 'blur'] }">
     * ```
     *
     * ### Highlighting the active link
     * This directive can be used in conjunction with [[uiSrefActive]] to highlight the active link.
     *
     * ### Notes
     *
     * - You can use `ui-params` to change **only the parameter values** by omitting the state name and supplying only `ui-state-params`.
     *   However, it might be simpler to use [[uiSref]] parameter-only links.
     *
     * #### Example:
     * Sets the `lang` parameter to `en` and remains on the same state.
     *
     * ```html
     * <a ui-state="" ui-state-params="{ lang: 'en' }">English</a>
     * ```
     *
     * - A middle-click, right-click, or ctrl-click is handled (natively) by the browser to open the href in a new window, for example.
     * ```
     */
    var uiStateDirective;
    uiStateDirective = [
        '$uiRouter',
        '$timeout',
        function $StateRefDynamicDirective($uiRouter, $timeout) {
            var $state = $uiRouter.stateService;
            return {
                restrict: 'A',
                require: ['?^uiSrefActive', '?^uiSrefActiveEq'],
                link: function (scope, element, attrs, uiSrefActive) {
                    var type = getTypeInfo(element);
                    var active = uiSrefActive[1] || uiSrefActive[0];
                    var unlinkInfoFn = null;
                    var hookFn;
                    var rawDef = {};
                    var getDef = function () { return processedDef($state, element, rawDef); };
                    var inputAttrs = ['uiState', 'uiStateParams', 'uiStateOpts'];
                    var watchDeregFns = inputAttrs.reduce(function (acc, attr) { return ((acc[attr] = noop), acc); }, {});
                    function update() {
                        var def = getDef();
                        if (unlinkInfoFn)
                            unlinkInfoFn();
                        if (active)
                            unlinkInfoFn = active.$$addStateInfo(def.uiState, def.uiStateParams);
                        if (def.href != null)
                            attrs.$set(type.attr, def.href);
                    }
                    inputAttrs.forEach(function (field) {
                        rawDef[field] = attrs[field] ? scope.$eval(attrs[field]) : null;
                        attrs.$observe(field, function (expr) {
                            watchDeregFns[field]();
                            watchDeregFns[field] = scope.$watch(expr, function (newval) {
                                rawDef[field] = newval;
                                update();
                            }, true);
                        });
                    });
                    update();
                    scope.$on('$destroy', $uiRouter.stateRegistry.onStatesChanged(update));
                    scope.$on('$destroy', $uiRouter.transitionService.onSuccess({}, update));
                    if (!type.clickable)
                        return;
                    hookFn = clickHook(element, $state, $timeout, type, getDef);
                    bindEvents(element, scope, hookFn, rawDef.uiStateOpts);
                },
            };
        },
    ];
    /**
     * `ui-sref-active` and `ui-sref-active-eq`: A directive that adds a CSS class when a `ui-sref` is active
     *
     * A directive working alongside [[uiSref]] and [[uiState]] to add classes to an element when the
     * related directive's state is active (and remove them when it is inactive).
     *
     * The primary use-case is to highlight the active link in navigation menus,
     * distinguishing it from the inactive menu items.
     *
     * ### Linking to a `ui-sref` or `ui-state`
     * `ui-sref-active` can live on the same element as `ui-sref`/`ui-state`, or it can be on a parent element.
     * If a `ui-sref-active` is a parent to more than one `ui-sref`/`ui-state`, it will apply the CSS class when **any of the links are active**.
     *
     * ### Matching
     *
     * The `ui-sref-active` directive applies the CSS class when the `ui-sref`/`ui-state`'s target state **or any child state is active**.
     * This is a "fuzzy match" which uses [[StateService.includes]].
     *
     * The `ui-sref-active-eq` directive applies the CSS class when the `ui-sref`/`ui-state`'s target state is directly active (not when child states are active).
     * This is an "exact match" which uses [[StateService.is]].
     *
     * ### Parameter values
     * If the `ui-sref`/`ui-state` includes parameter values, the current parameter values must match the link's values for the link to be highlighted.
     * This allows a list of links to the same state with different parameters to be rendered, and the correct one highlighted.
     *
     * #### Example:
     * ```html
     * <li ng-repeat="user in users" ui-sref-active="active">
     *   <a ui-sref="user.details({ userId: user.id })">{{ user.lastName }}</a>
     * </li>
     * ```
     *
     * ### Examples
     *
     * Given the following template:
     * #### Example:
     * ```html
     * <ul>
     *   <li ui-sref-active="active" class="item">
     *     <a href ui-sref="app.user({user: 'bilbobaggins'})">@bilbobaggins</a>
     *   </li>
     * </ul>
     * ```
     *
     * When the app state is `app.user` (or any child state),
     * and contains the state parameter "user" with value "bilbobaggins",
     * the resulting HTML will appear as (note the 'active' class):
     *
     * ```html
     * <ul>
     *   <li ui-sref-active="active" class="item active">
     *     <a ui-sref="app.user({user: 'bilbobaggins'})" href="/users/bilbobaggins">@bilbobaggins</a>
     *   </li>
     * </ul>
     * ```
     *
     * ### Glob mode
     *
     * It is possible to pass `ui-sref-active` an expression that evaluates to an object.
     * The objects keys represent active class names and values represent the respective state names/globs.
     * `ui-sref-active` will match if the current active state **includes** any of
     * the specified state names/globs, even the abstract ones.
     *
     * #### Example:
     * Given the following template, with "admin" being an abstract state:
     * ```html
     * <div ui-sref-active="{'active': 'admin.**'}">
     *   <a ui-sref-active="active" ui-sref="admin.roles">Roles</a>
     * </div>
     * ```
     *
     * Arrays are also supported as values in the `ngClass`-like interface.
     * This allows multiple states to add `active` class.
     *
     * #### Example:
     * Given the following template, with "admin.roles" being the current state, the class will be added too:
     * ```html
     * <div ui-sref-active="{'active': ['owner.**', 'admin.**']}">
     *   <a ui-sref-active="active" ui-sref="admin.roles">Roles</a>
     * </div>
     * ```
     *
     * When the current state is "admin.roles" the "active" class will be applied to both the `<div>` and `<a>` elements.
     * It is important to note that the state names/globs passed to `ui-sref-active` override any state provided by a linked `ui-sref`.
     *
     * ### Notes:
     *
     * - The class name is interpolated **once** during the directives link time (any further changes to the
     * interpolated value are ignored).
     *
     * - Multiple classes may be specified in a space-separated format: `ui-sref-active='class1 class2 class3'`
     */
    var uiSrefActiveDirective;
    uiSrefActiveDirective = [
        '$state',
        '$stateParams',
        '$interpolate',
        '$uiRouter',
        function $StateRefActiveDirective($state, $stateParams, $interpolate, $uiRouter) {
            return {
                restrict: 'A',
                controller: [
                    '$scope',
                    '$element',
                    '$attrs',
                    function ($scope, $element, $attrs) {
                        var states = [];
                        var activeEqClass;
                        var uiSrefActive;
                        // There probably isn't much point in $observing this
                        // uiSrefActive and uiSrefActiveEq share the same directive object with some
                        // slight difference in logic routing
                        activeEqClass = $interpolate($attrs.uiSrefActiveEq || '', false)($scope);
                        try {
                            uiSrefActive = $scope.$eval($attrs.uiSrefActive);
                        }
                        catch (e) {
                            // Do nothing. uiSrefActive is not a valid expression.
                            // Fall back to using $interpolate below
                        }
                        uiSrefActive = uiSrefActive || $interpolate($attrs.uiSrefActive || '', false)($scope);
                        setStatesFromDefinitionObject(uiSrefActive);
                        // Allow uiSref to communicate with uiSrefActive[Equals]
                        this.$$addStateInfo = function (newState, newParams) {
                            // we already got an explicit state provided by ui-sref-active, so we
                            // shadow the one that comes from ui-sref
                            if (isObject(uiSrefActive) && states.length > 0) {
                                return;
                            }
                            var deregister = addState(newState, newParams, uiSrefActive);
                            update();
                            return deregister;
                        };
                        function updateAfterTransition(trans) {
                            trans.promise.then(update, noop);
                        }
                        $scope.$on('$destroy', setupEventListeners());
                        if ($uiRouter.globals.transition) {
                            updateAfterTransition($uiRouter.globals.transition);
                        }
                        function setupEventListeners() {
                            var deregisterStatesChangedListener = $uiRouter.stateRegistry.onStatesChanged(handleStatesChanged);
                            var deregisterOnStartListener = $uiRouter.transitionService.onStart({}, updateAfterTransition);
                            var deregisterStateChangeSuccessListener = $scope.$on('$stateChangeSuccess', update);
                            return function cleanUp() {
                                deregisterStatesChangedListener();
                                deregisterOnStartListener();
                                deregisterStateChangeSuccessListener();
                            };
                        }
                        function handleStatesChanged() {
                            setStatesFromDefinitionObject(uiSrefActive);
                        }
                        function setStatesFromDefinitionObject(statesDefinition) {
                            if (isObject(statesDefinition)) {
                                states = [];
                                forEach(statesDefinition, function (stateOrName, activeClass) {
                                    // Helper function to abstract adding state.
                                    var addStateForClass = function (stateOrName, activeClass) {
                                        var ref = parseStateRef(stateOrName);
                                        addState(ref.state, $scope.$eval(ref.paramExpr), activeClass);
                                    };
                                    if (isString(stateOrName)) {
                                        // If state is string, just add it.
                                        addStateForClass(stateOrName, activeClass);
                                    }
                                    else if (isArray(stateOrName)) {
                                        // If state is an array, iterate over it and add each array item individually.
                                        forEach(stateOrName, function (stateOrName) {
                                            addStateForClass(stateOrName, activeClass);
                                        });
                                    }
                                });
                            }
                        }
                        function addState(stateName, stateParams, activeClass) {
                            var state = $state.get(stateName, stateContext($element));
                            var stateInfo = {
                                state: state || { name: stateName },
                                params: stateParams,
                                activeClass: activeClass,
                            };
                            states.push(stateInfo);
                            return function removeState() {
                                removeFrom(states)(stateInfo);
                            };
                        }
                        // Update route state
                        function update() {
                            var splitClasses = function (str) { return str.split(/\s/).filter(identity); };
                            var getClasses = function (stateList) {
                                return stateList
                                    .map(function (x) { return x.activeClass; })
                                    .map(splitClasses)
                                    .reduce(unnestR, []);
                            };
                            var allClasses = getClasses(states).concat(splitClasses(activeEqClass)).reduce(uniqR, []);
                            var fuzzyClasses = getClasses(states.filter(function (x) { return $state.includes(x.state.name, x.params); }));
                            var exactlyMatchesAny = !!states.filter(function (x) { return $state.is(x.state.name, x.params); }).length;
                            var exactClasses = exactlyMatchesAny ? splitClasses(activeEqClass) : [];
                            var addClasses = fuzzyClasses.concat(exactClasses).reduce(uniqR, []);
                            var removeClasses = allClasses.filter(function (cls) { return !inArray(addClasses, cls); });
                            $scope.$evalAsync(function () {
                                addClasses.forEach(function (className) { return $element.addClass(className); });
                                removeClasses.forEach(function (className) { return $element.removeClass(className); });
                            });
                        }
                        update();
                    },
                ],
            };
        },
    ];
    ng
        .module('ui.router.state')
        .directive('uiSref', uiSrefDirective)
        .directive('uiSrefActive', uiSrefActiveDirective)
        .directive('uiSrefActiveEq', uiSrefActiveDirective)
        .directive('uiState', uiStateDirective);

    /** @publicapi @module ng1 */ /** */
    /**
     * `isState` Filter: truthy if the current state is the parameter
     *
     * Translates to [[StateService.is]] `$state.is("stateName")`.
     *
     * #### Example:
     * ```html
     * <div ng-if="'stateName' | isState">show if state is 'stateName'</div>
     * ```
     */
    $IsStateFilter.$inject = ['$state'];
    function $IsStateFilter($state) {
        var isFilter = function (state, params, options) {
            return $state.is(state, params, options);
        };
        isFilter.$stateful = true;
        return isFilter;
    }
    /**
     * `includedByState` Filter: truthy if the current state includes the parameter
     *
     * Translates to [[StateService.includes]]` $state.is("fullOrPartialStateName")`.
     *
     * #### Example:
     * ```html
     * <div ng-if="'fullOrPartialStateName' | includedByState">show if state includes 'fullOrPartialStateName'</div>
     * ```
     */
    $IncludedByStateFilter.$inject = ['$state'];
    function $IncludedByStateFilter($state) {
        var includesFilter = function (state, params, options) {
            return $state.includes(state, params, options);
        };
        includesFilter.$stateful = true;
        return includesFilter;
    }
    ng.module('ui.router.state').filter('isState', $IsStateFilter).filter('includedByState', $IncludedByStateFilter);

    /** @publicapi @module directives */ /** */
    /**
     * `ui-view`: A viewport directive which is filled in by a view from the active state.
     *
     * ### Attributes
     *
     * - `name`: (Optional) A view name.
     *   The name should be unique amongst the other views in the same state.
     *   You can have views of the same name that live in different states.
     *   The ui-view can be targeted in a View using the name ([[Ng1StateDeclaration.views]]).
     *
     * - `autoscroll`: an expression. When it evaluates to true, the `ui-view` will be scrolled into view when it is activated.
     *   Uses [[$uiViewScroll]] to do the scrolling.
     *
     * - `onload`: Expression to evaluate whenever the view updates.
     *
     * #### Example:
     * A view can be unnamed or named.
     * ```html
     * <!-- Unnamed -->
     * <div ui-view></div>
     *
     * <!-- Named -->
     * <div ui-view="viewName"></div>
     *
     * <!-- Named (different style) -->
     * <ui-view name="viewName"></ui-view>
     * ```
     *
     * You can only have one unnamed view within any template (or root html). If you are only using a
     * single view and it is unnamed then you can populate it like so:
     *
     * ```html
     * <div ui-view></div>
     * $stateProvider.state("home", {
     *   template: "<h1>HELLO!</h1>"
     * })
     * ```
     *
     * The above is a convenient shortcut equivalent to specifying your view explicitly with the
     * [[Ng1StateDeclaration.views]] config property, by name, in this case an empty name:
     *
     * ```js
     * $stateProvider.state("home", {
     *   views: {
     *     "": {
     *       template: "<h1>HELLO!</h1>"
     *     }
     *   }
     * })
     * ```
     *
     * But typically you'll only use the views property if you name your view or have more than one view
     * in the same template. There's not really a compelling reason to name a view if its the only one,
     * but you could if you wanted, like so:
     *
     * ```html
     * <div ui-view="main"></div>
     * ```
     *
     * ```js
     * $stateProvider.state("home", {
     *   views: {
     *     "main": {
     *       template: "<h1>HELLO!</h1>"
     *     }
     *   }
     * })
     * ```
     *
     * Really though, you'll use views to set up multiple views:
     *
     * ```html
     * <div ui-view></div>
     * <div ui-view="chart"></div>
     * <div ui-view="data"></div>
     * ```
     *
     * ```js
     * $stateProvider.state("home", {
     *   views: {
     *     "": {
     *       template: "<h1>HELLO!</h1>"
     *     },
     *     "chart": {
     *       template: "<chart_thing/>"
     *     },
     *     "data": {
     *       template: "<data_thing/>"
     *     }
     *   }
     * })
     * ```
     *
     * #### Examples for `autoscroll`:
     * ```html
     * <!-- If autoscroll present with no expression,
     *      then scroll ui-view into view -->
     * <ui-view autoscroll/>
     *
     * <!-- If autoscroll present with valid expression,
     *      then scroll ui-view into view if expression evaluates to true -->
     * <ui-view autoscroll='true'/>
     * <ui-view autoscroll='false'/>
     * <ui-view autoscroll='scopeVariable'/>
     * ```
     *
     * Resolve data:
     *
     * The resolved data from the state's `resolve` block is placed on the scope as `$resolve` (this
     * can be customized using [[Ng1ViewDeclaration.resolveAs]]).  This can be then accessed from the template.
     *
     * Note that when `controllerAs` is being used, `$resolve` is set on the controller instance *after* the
     * controller is instantiated.  The `$onInit()` hook can be used to perform initialization code which
     * depends on `$resolve` data.
     *
     * #### Example:
     * ```js
     * $stateProvider.state('home', {
     *   template: '<my-component user="$resolve.user"></my-component>',
     *   resolve: {
     *     user: function(UserService) { return UserService.fetchUser(); }
     *   }
     * });
     * ```
     */
    var uiView;
    // eslint-disable-next-line prefer-const
    uiView = [
        '$view',
        '$animate',
        '$uiViewScroll',
        '$interpolate',
        '$q',
        function $ViewDirective($view, $animate, $uiViewScroll, $interpolate, $q) {
            function getRenderer() {
                return {
                    enter: function (element, target, cb) {
                        if (ng.version.minor > 2) {
                            $animate.enter(element, null, target).then(cb);
                        }
                        else {
                            $animate.enter(element, null, target, cb);
                        }
                    },
                    leave: function (element, cb) {
                        if (ng.version.minor > 2) {
                            $animate.leave(element).then(cb);
                        }
                        else {
                            $animate.leave(element, cb);
                        }
                    },
                };
            }
            function configsEqual(config1, config2) {
                return config1 === config2;
            }
            var rootData = {
                $cfg: { viewDecl: { $context: $view._pluginapi._rootViewContext() } },
                $uiView: {},
            };
            var directive = {
                count: 0,
                restrict: 'ECA',
                terminal: true,
                priority: 400,
                transclude: 'element',
                compile: function (tElement, tAttrs, $transclude) {
                    return function (scope, $element, attrs) {
                        var onloadExp = attrs['onload'] || '', autoScrollExp = attrs['autoscroll'], renderer = getRenderer(), inherited = $element.inheritedData('$uiView') || rootData, name = $interpolate(attrs['uiView'] || attrs['name'] || '')(scope) || '$default';
                        var previousEl, currentEl, currentScope, viewConfig;
                        var activeUIView = {
                            $type: 'ng1',
                            id: directive.count++,
                            name: name,
                            fqn: inherited.$uiView.fqn ? inherited.$uiView.fqn + '.' + name : name,
                            config: null,
                            configUpdated: configUpdatedCallback,
                            get creationContext() {
                                // The context in which this ui-view "tag" was created
                                var fromParentTagConfig = parse('$cfg.viewDecl.$context')(inherited);
                                // Allow <ui-view name="foo"><ui-view name="bar"></ui-view></ui-view>
                                // See https://github.com/angular-ui/ui-router/issues/3355
                                var fromParentTag = parse('$uiView.creationContext')(inherited);
                                return fromParentTagConfig || fromParentTag;
                            },
                        };
                        trace.traceUIViewEvent('Linking', activeUIView);
                        function configUpdatedCallback(config) {
                            if (config && !(config instanceof Ng1ViewConfig))
                                return;
                            if (configsEqual(viewConfig, config))
                                return;
                            trace.traceUIViewConfigUpdated(activeUIView, config && config.viewDecl && config.viewDecl.$context);
                            viewConfig = config;
                            updateView(config);
                        }
                        $element.data('$uiView', { $uiView: activeUIView });
                        updateView();
                        var unregister = $view.registerUIView(activeUIView);
                        scope.$on('$destroy', function () {
                            trace.traceUIViewEvent('Destroying/Unregistering', activeUIView);
                            unregister();
                        });
                        function cleanupLastView() {
                            if (previousEl) {
                                trace.traceUIViewEvent('Removing (previous) el', previousEl.data('$uiView'));
                                previousEl.remove();
                                previousEl = null;
                            }
                            if (currentScope) {
                                trace.traceUIViewEvent('Destroying scope', activeUIView);
                                currentScope.$destroy();
                                currentScope = null;
                            }
                            if (currentEl) {
                                var _viewData_1 = currentEl.data('$uiViewAnim');
                                trace.traceUIViewEvent('Animate out', _viewData_1);
                                renderer.leave(currentEl, function () {
                                    _viewData_1.$$animLeave.resolve();
                                    previousEl = null;
                                });
                                previousEl = currentEl;
                                currentEl = null;
                            }
                        }
                        function updateView(config) {
                            var newScope = scope.$new();
                            var animEnter = $q.defer(), animLeave = $q.defer();
                            var $uiViewData = {
                                $cfg: config,
                                $uiView: activeUIView,
                            };
                            var $uiViewAnim = {
                                $animEnter: animEnter.promise,
                                $animLeave: animLeave.promise,
                                $$animLeave: animLeave,
                            };
                            /**
                             * @ngdoc event
                             * @name ui.router.state.directive:ui-view#$viewContentLoading
                             * @eventOf ui.router.state.directive:ui-view
                             * @eventType emits on ui-view directive scope
                             * @description
                             *
                             * Fired once the view **begins loading**, *before* the DOM is rendered.
                             *
                             * @param {Object} event Event object.
                             * @param {string} viewName Name of the view.
                             */
                            newScope.$emit('$viewContentLoading', name);
                            var cloned = $transclude(newScope, function (clone) {
                                clone.data('$uiViewAnim', $uiViewAnim);
                                clone.data('$uiView', $uiViewData);
                                renderer.enter(clone, $element, function onUIViewEnter() {
                                    animEnter.resolve();
                                    if (currentScope)
                                        currentScope.$emit('$viewContentAnimationEnded');
                                    if ((isDefined(autoScrollExp) && !autoScrollExp) || scope.$eval(autoScrollExp)) {
                                        $uiViewScroll(clone);
                                    }
                                });
                                cleanupLastView();
                            });
                            currentEl = cloned;
                            currentScope = newScope;
                            /**
                             * @ngdoc event
                             * @name ui.router.state.directive:ui-view#$viewContentLoaded
                             * @eventOf ui.router.state.directive:ui-view
                             * @eventType emits on ui-view directive scope
                             * @description           *
                             * Fired once the view is **loaded**, *after* the DOM is rendered.
                             *
                             * @param {Object} event Event object.
                             */
                            currentScope.$emit('$viewContentLoaded', config || viewConfig);
                            currentScope.$eval(onloadExp);
                        }
                    };
                },
            };
            return directive;
        },
    ];
    $ViewDirectiveFill.$inject = ['$compile', '$controller', '$transitions', '$view', '$q'];
    /** @hidden */
    function $ViewDirectiveFill($compile, $controller, $transitions, $view, $q) {
        var getControllerAs = parse('viewDecl.controllerAs');
        var getResolveAs = parse('viewDecl.resolveAs');
        return {
            restrict: 'ECA',
            priority: -400,
            compile: function (tElement) {
                var initial = tElement.html();
                tElement.empty();
                return function (scope, $element) {
                    var data = $element.data('$uiView');
                    if (!data) {
                        $element.html(initial);
                        $compile($element.contents())(scope);
                        return;
                    }
                    var cfg = data.$cfg || { viewDecl: {}, getTemplate: noop };
                    var resolveCtx = cfg.path && new ResolveContext(cfg.path);
                    $element.html(cfg.getTemplate($element, resolveCtx) || initial);
                    trace.traceUIViewFill(data.$uiView, $element.html());
                    var link = $compile($element.contents());
                    var controller = cfg.controller;
                    var controllerAs = getControllerAs(cfg);
                    var resolveAs = getResolveAs(cfg);
                    var locals = resolveCtx && getLocals(resolveCtx);
                    scope[resolveAs] = locals;
                    if (controller) {
                        var controllerInstance = ($controller(controller, extend({}, locals, { $scope: scope, $element: $element })));
                        if (controllerAs) {
                            scope[controllerAs] = controllerInstance;
                            scope[controllerAs][resolveAs] = locals;
                        }
                        // TODO: Use $view service as a central point for registering component-level hooks
                        // Then, when a component is created, tell the $view service, so it can invoke hooks
                        // $view.componentLoaded(controllerInstance, { $scope: scope, $element: $element });
                        // scope.$on('$destroy', () => $view.componentUnloaded(controllerInstance, { $scope: scope, $element: $element }));
                        $element.data('$ngControllerController', controllerInstance);
                        $element.children().data('$ngControllerController', controllerInstance);
                        registerControllerCallbacks($q, $transitions, controllerInstance, scope, cfg);
                    }
                    // Wait for the component to appear in the DOM
                    if (isString(cfg.component)) {
                        var kebobName = kebobString(cfg.component);
                        var tagRegexp_1 = new RegExp("^(x-|data-)?" + kebobName + "$", 'i');
                        var getComponentController = function () {
                            var directiveEl = [].slice
                                .call($element[0].children)
                                .filter(function (el) { return el && el.tagName && tagRegexp_1.exec(el.tagName); });
                            return directiveEl && ng.element(directiveEl).data("$" + cfg.component + "Controller");
                        };
                        var deregisterWatch_1 = scope.$watch(getComponentController, function (ctrlInstance) {
                            if (!ctrlInstance)
                                return;
                            registerControllerCallbacks($q, $transitions, ctrlInstance, scope, cfg);
                            deregisterWatch_1();
                        });
                    }
                    link(scope);
                };
            },
        };
    }
    /** @hidden */
    var hasComponentImpl = typeof ng.module('ui.router')['component'] === 'function';
    /** @hidden incrementing id */
    var _uiCanExitId = 0;
    /** @hidden TODO: move these callbacks to $view and/or `/hooks/components.ts` or something */
    function registerControllerCallbacks($q, $transitions, controllerInstance, $scope, cfg) {
        // Call $onInit() ASAP
        if (isFunction(controllerInstance.$onInit) &&
            !((cfg.viewDecl.component || cfg.viewDecl.componentProvider) && hasComponentImpl)) {
            controllerInstance.$onInit();
        }
        var viewState = tail(cfg.path).state.self;
        var hookOptions = { bind: controllerInstance };
        // Add component-level hook for onUiParamsChanged
        if (isFunction(controllerInstance.uiOnParamsChanged)) {
            var resolveContext = new ResolveContext(cfg.path);
            var viewCreationTrans_1 = resolveContext.getResolvable('$transition$').data;
            // Fire callback on any successful transition
            var paramsUpdated = function ($transition$) {
                // Exit early if the $transition$ is the same as the view was created within.
                // Exit early if the $transition$ will exit the state the view is for.
                if ($transition$ === viewCreationTrans_1 || $transition$.exiting().indexOf(viewState) !== -1)
                    return;
                var toParams = $transition$.params('to');
                var fromParams = $transition$.params('from');
                var getNodeSchema = function (node) { return node.paramSchema; };
                var toSchema = $transition$.treeChanges('to').map(getNodeSchema).reduce(unnestR, []);
                var fromSchema = $transition$.treeChanges('from').map(getNodeSchema).reduce(unnestR, []);
                // Find the to params that have different values than the from params
                var changedToParams = toSchema.filter(function (param) {
                    var idx = fromSchema.indexOf(param);
                    return idx === -1 || !fromSchema[idx].type.equals(toParams[param.id], fromParams[param.id]);
                });
                // Only trigger callback if a to param has changed or is new
                if (changedToParams.length) {
                    var changedKeys_1 = changedToParams.map(function (x) { return x.id; });
                    // Filter the params to only changed/new to params.  `$transition$.params()` may be used to get all params.
                    var newValues = filter(toParams, function (val, key) { return changedKeys_1.indexOf(key) !== -1; });
                    controllerInstance.uiOnParamsChanged(newValues, $transition$);
                }
            };
            $scope.$on('$destroy', $transitions.onSuccess({}, paramsUpdated, hookOptions));
        }
        // Add component-level hook for uiCanExit
        if (isFunction(controllerInstance.uiCanExit)) {
            var id_1 = _uiCanExitId++;
            var cacheProp_1 = '_uiCanExitIds';
            // Returns true if a redirect transition already answered truthy
            var prevTruthyAnswer_1 = function (trans) {
                return !!trans && ((trans[cacheProp_1] && trans[cacheProp_1][id_1] === true) || prevTruthyAnswer_1(trans.redirectedFrom()));
            };
            // If a user answered yes, but the transition was later redirected, don't also ask for the new redirect transition
            var wrappedHook = function (trans) {
                var promise;
                var ids = (trans[cacheProp_1] = trans[cacheProp_1] || {});
                if (!prevTruthyAnswer_1(trans)) {
                    promise = $q.when(controllerInstance.uiCanExit(trans));
                    promise.then(function (val) { return (ids[id_1] = val !== false); });
                }
                return promise;
            };
            var criteria = { exiting: viewState.name };
            $scope.$on('$destroy', $transitions.onBefore(criteria, wrappedHook, hookOptions));
        }
    }
    ng.module('ui.router.state').directive('uiView', uiView);
    ng.module('ui.router.state').directive('uiView', $ViewDirectiveFill);

    /** @publicapi @module ng1 */ /** */
    /** @hidden */
    function $ViewScrollProvider() {
        var useAnchorScroll = false;
        this.useAnchorScroll = function () {
            useAnchorScroll = true;
        };
        this.$get = [
            '$anchorScroll',
            '$timeout',
            function ($anchorScroll, $timeout) {
                if (useAnchorScroll) {
                    return $anchorScroll;
                }
                return function ($element) {
                    return $timeout(function () {
                        $element[0].scrollIntoView();
                    }, 0, false);
                };
            },
        ];
    }
    ng.module('ui.router.state').provider('$uiViewScroll', $ViewScrollProvider);

    /**
     * Main entry point for angular 1.x build
     * @publicapi @module ng1
     */ /** */
    var index$1 = 'ui.router';

    exports.$injector = $injector;
    exports.$q = $q;
    exports.BaseLocationServices = BaseLocationServices;
    exports.BaseUrlRule = BaseUrlRule;
    exports.BrowserLocationConfig = BrowserLocationConfig;
    exports.Glob = Glob;
    exports.HashLocationService = HashLocationService;
    exports.HookBuilder = HookBuilder;
    exports.MemoryLocationConfig = MemoryLocationConfig;
    exports.MemoryLocationService = MemoryLocationService;
    exports.NATIVE_INJECTOR_TOKEN = NATIVE_INJECTOR_TOKEN;
    exports.Ng1ViewConfig = Ng1ViewConfig;
    exports.Param = Param;
    exports.ParamFactory = ParamFactory;
    exports.ParamType = ParamType;
    exports.ParamTypes = ParamTypes;
    exports.PathNode = PathNode;
    exports.PathUtils = PathUtils;
    exports.PushStateLocationService = PushStateLocationService;
    exports.Queue = Queue;
    exports.RegisteredHook = RegisteredHook;
    exports.Rejection = Rejection;
    exports.Resolvable = Resolvable;
    exports.ResolveContext = ResolveContext;
    exports.StateBuilder = StateBuilder;
    exports.StateMatcher = StateMatcher;
    exports.StateObject = StateObject;
    exports.StateParams = StateParams;
    exports.StateProvider = StateProvider;
    exports.StateQueueManager = StateQueueManager;
    exports.StateRegistry = StateRegistry;
    exports.StateService = StateService;
    exports.TargetState = TargetState;
    exports.Trace = Trace;
    exports.Transition = Transition;
    exports.TransitionEventType = TransitionEventType;
    exports.TransitionHook = TransitionHook;
    exports.TransitionService = TransitionService;
    exports.UIRouter = UIRouter;
    exports.UIRouterGlobals = UIRouterGlobals;
    exports.UIRouterPluginBase = UIRouterPluginBase;
    exports.UrlConfig = UrlConfig;
    exports.UrlMatcher = UrlMatcher;
    exports.UrlMatcherFactory = UrlMatcherFactory;
    exports.UrlRouter = UrlRouter;
    exports.UrlRouterProvider = UrlRouterProvider;
    exports.UrlRuleFactory = UrlRuleFactory;
    exports.UrlRules = UrlRules;
    exports.UrlService = UrlService;
    exports.ViewService = ViewService;
    exports._extend = _extend;
    exports._inArray = _inArray;
    exports._pushTo = _pushTo;
    exports._removeFrom = _removeFrom;
    exports.all = all;
    exports.allTrueR = allTrueR;
    exports.ancestors = ancestors;
    exports.and = and;
    exports.any = any;
    exports.anyTrueR = anyTrueR;
    exports.applyPairs = applyPairs;
    exports.arrayTuples = arrayTuples;
    exports.assertFn = assertFn;
    exports.assertMap = assertMap;
    exports.assertPredicate = assertPredicate;
    exports.beforeAfterSubstr = beforeAfterSubstr;
    exports.buildUrl = buildUrl;
    exports.compose = compose;
    exports.copy = copy;
    exports.core = index;
    exports.createProxyFunctions = createProxyFunctions;
    exports.curry = curry;
    exports.default = index$1;
    exports.defaultResolvePolicy = defaultResolvePolicy;
    exports.defaultTransOpts = defaultTransOpts;
    exports.defaults = defaults;
    exports.deregAll = deregAll;
    exports.eq = eq;
    exports.equals = equals;
    exports.extend = extend;
    exports.filter = filter;
    exports.find = find;
    exports.flatten = flatten;
    exports.flattenR = flattenR;
    exports.fnToString = fnToString;
    exports.forEach = forEach;
    exports.fromJson = fromJson;
    exports.functionToString = functionToString;
    exports.getLocals = getLocals;
    exports.getNg1ViewConfigFactory = getNg1ViewConfigFactory;
    exports.getParams = getParams;
    exports.hashLocationPlugin = hashLocationPlugin;
    exports.hostRegex = hostRegex;
    exports.identity = identity;
    exports.inArray = inArray;
    exports.inherit = inherit;
    exports.invoke = invoke;
    exports.is = is;
    exports.isArray = isArray;
    exports.isDate = isDate;
    exports.isDefined = isDefined;
    exports.isFunction = isFunction;
    exports.isInjectable = isInjectable;
    exports.isNull = isNull;
    exports.isNullOrUndefined = isNullOrUndefined;
    exports.isNumber = isNumber;
    exports.isObject = isObject;
    exports.isPromise = isPromise;
    exports.isRegExp = isRegExp;
    exports.isString = isString;
    exports.isUndefined = isUndefined;
    exports.joinNeighborsR = joinNeighborsR;
    exports.kebobString = kebobString;
    exports.keyValsToObjectR = keyValsToObjectR;
    exports.locationPluginFactory = locationPluginFactory;
    exports.makeEvent = makeEvent;
    exports.makeStub = makeStub;
    exports.map = map;
    exports.mapObj = mapObj;
    exports.matchState = matchState;
    exports.maxLength = maxLength;
    exports.memoryLocationPlugin = memoryLocationPlugin;
    exports.mergeR = mergeR;
    exports.ng1ViewsBuilder = ng1ViewsBuilder;
    exports.noop = noop;
    exports.not = not;
    exports.omit = omit;
    exports.or = or;
    exports.padString = padString;
    exports.pairs = pairs;
    exports.parse = parse;
    exports.parseUrl = parseUrl$1;
    exports.pattern = pattern;
    exports.pick = pick;
    exports.pipe = pipe;
    exports.pluck = pluck;
    exports.prop = prop;
    exports.propEq = propEq;
    exports.pushR = pushR;
    exports.pushStateLocationPlugin = pushStateLocationPlugin;
    exports.pushTo = pushTo;
    exports.removeFrom = removeFrom;
    exports.resolvablesBuilder = resolvablesBuilder;
    exports.resolvePolicies = resolvePolicies;
    exports.root = root;
    exports.services = services;
    exports.servicesPlugin = servicesPlugin;
    exports.silenceUncaughtInPromise = silenceUncaughtInPromise;
    exports.silentRejection = silentRejection;
    exports.splitEqual = splitEqual;
    exports.splitHash = splitHash;
    exports.splitOnDelim = splitOnDelim;
    exports.splitQuery = splitQuery;
    exports.stringify = stringify;
    exports.stripLastPathElement = stripLastPathElement;
    exports.tail = tail;
    exports.toJson = toJson;
    exports.trace = trace;
    exports.trimHashVal = trimHashVal;
    exports.uniqR = uniqR;
    exports.unnest = unnest;
    exports.unnestR = unnestR;
    exports.val = val;
    exports.values = values;
    exports.watchDigests = watchDigests;

    Object.defineProperty(exports, '__esModule', { value: true });

})));
//# sourceMappingURL=angular-ui-router.js.map

/*! angular-breadcrumb - v0.4.1
* http://ncuillery.github.io/angular-breadcrumb
* Copyright (c) 2015 Nicolas Cuillery; Licensed MIT */

(function (window, angular, undefined) {
'use strict';

function isAOlderThanB(scopeA, scopeB) {
    if(angular.equals(scopeA.length, scopeB.length)) {
        return scopeA > scopeB;
    } else {
        return scopeA.length > scopeB.length;
    }
}

function parseStateRef(ref) {
    var parsed = ref.replace(/\n/g, " ").match(/^([^(]+?)\s*(\((.*)\))?$/);
    if (!parsed || parsed.length !== 4) { throw new Error("Invalid state ref '" + ref + "'"); }
    return { state: parsed[1], paramExpr: parsed[3] || null };
}

function $Breadcrumb() {

    var $$options = {
        prefixStateName: null,
        template: 'bootstrap3',
        templateUrl: null,
        includeAbstract : false
    };

    this.setOptions = function(options) {
        angular.extend($$options, options);
    };

    this.$get = ['$state', '$stateParams', '$rootScope', function($state, $stateParams, $rootScope) {

        var $lastViewScope = $rootScope;

        // Early catch of $viewContentLoaded event
        $rootScope.$on('$viewContentLoaded', function (event) {
            // With nested views, the event occur several times, in "wrong" order
            if(!event.targetScope.ncyBreadcrumbIgnore &&
                isAOlderThanB(event.targetScope.$id, $lastViewScope.$id)) {
                $lastViewScope = event.targetScope;
            }
        });

        // Get the parent state
        var $$parentState = function(state) {
            // Check if state has explicit parent OR we try guess parent from its name
            var parent = state.parent || (/^(.+)\.[^.]+$/.exec(state.name) || [])[1];
            var isObjectParent = typeof parent === "object";
            // if parent is a object reference, then extract the name
            return isObjectParent ? parent.name : parent;
        };

        // Add the state in the chain if not already in and if not abstract
        var $$addStateInChain = function(chain, stateRef) {
            var conf,
                parentParams,
                ref = parseStateRef(stateRef),
                force = false,
                skip = false;

            for(var i=0, l=chain.length; i<l; i+=1) {
                if (chain[i].name === ref.state) {
                    return;
                }
            }

            conf = $state.get(ref.state);
            // Get breadcrumb options
            if(conf.ncyBreadcrumb) {
                if(conf.ncyBreadcrumb.force){ force = true; }
                if(conf.ncyBreadcrumb.skip){ skip = true; }
            }
            if((!conf.abstract || $$options.includeAbstract || force) && !skip) {
                if(ref.paramExpr) {
                    parentParams = $lastViewScope.$eval(ref.paramExpr);
                }

                conf.ncyBreadcrumbLink = $state.href(ref.state, parentParams || $stateParams || {});
                chain.unshift(conf);
            }
        };

        // Get the state for the parent step in the breadcrumb
        var $$breadcrumbParentState = function(stateRef) {
            var ref = parseStateRef(stateRef),
                conf = $state.get(ref.state);

            if(conf.ncyBreadcrumb && conf.ncyBreadcrumb.parent) {
                // Handle the "parent" property of the breadcrumb, override the parent/child relation of the state
                var isFunction = typeof conf.ncyBreadcrumb.parent === 'function';
                var parentStateRef = isFunction ? conf.ncyBreadcrumb.parent($lastViewScope) : conf.ncyBreadcrumb.parent;
                if(parentStateRef) {
                    return parentStateRef;
                }
            }

            return $$parentState(conf);
        };

        return {

            getTemplate: function(templates) {
                if($$options.templateUrl) {
                    // templateUrl takes precedence over template
                    return null;
                } else if(templates[$$options.template]) {
                    // Predefined templates (bootstrap, ...)
                    return templates[$$options.template];
                } else {
                    return $$options.template;
                }
            },

            getTemplateUrl: function() {
                return $$options.templateUrl;
            },

            getStatesChain: function(exitOnFirst) { // Deliberately undocumented param, see getLastStep
                var chain = [];

                // From current state to the root
                for(var stateRef = $state.$current.self.name; stateRef; stateRef=$$breadcrumbParentState(stateRef)) {
                    $$addStateInChain(chain, stateRef);
                    if(exitOnFirst && chain.length) {
                        return chain;
                    }
                }

                // Prefix state treatment
                if($$options.prefixStateName) {
                    $$addStateInChain(chain, $$options.prefixStateName);
                }

                return chain;
            },

            getLastStep: function() {
                var chain = this.getStatesChain(true);
                return chain.length ? chain[0] : undefined;
            },

            $getLastViewScope: function() {
                return $lastViewScope;
            }
        };
    }];
}

var getExpression = function(interpolationFunction) {
    if(interpolationFunction.expressions) {
        return interpolationFunction.expressions;
    } else {
        var expressions = [];
        angular.forEach(interpolationFunction.parts, function(part) {
            if(angular.isFunction(part)) {
                expressions.push(part.exp);
            }
        });
        return expressions;
    }
};

var registerWatchers = function(labelWatcherArray, interpolationFunction, viewScope, step) {
    angular.forEach(getExpression(interpolationFunction), function(expression) {
        var watcher = viewScope.$watch(expression, function() {
            step.ncyBreadcrumbLabel = interpolationFunction(viewScope);
        });
        labelWatcherArray.push(watcher);
    });

};

var deregisterWatchers = function(labelWatcherArray) {
    angular.forEach(labelWatcherArray, function(deregisterWatch) {
        deregisterWatch();
    });
};

function BreadcrumbDirective($interpolate, $breadcrumb, $rootScope) {
    var $$templates = {
        bootstrap2: '<ul class="breadcrumb">' +
            '<li ng-repeat="step in steps" ng-switch="$last || !!step.abstract" ng-class="{active: $last}">' +
            '<a ng-switch-when="false" href="{{step.ncyBreadcrumbLink}}">{{step.ncyBreadcrumbLabel}}</a>' +
            '<span ng-switch-when="true">{{step.ncyBreadcrumbLabel}}</span>' +
            '<span class="divider" ng-hide="$last">/</span>' +
            '</li>' +
            '</ul>',
        bootstrap3: '<ol class="breadcrumb">' +
            '<li ng-repeat="step in steps" ng-class="{active: $last}" ng-switch="$last || !!step.abstract">' +
            '<a ng-switch-when="false" href="{{step.ncyBreadcrumbLink}}">{{step.ncyBreadcrumbLabel}}</a>' +
            '<span ng-switch-when="true">{{step.ncyBreadcrumbLabel}}</span>' +
            '</li>' +
            '</ol>'
    };

    return {
        restrict: 'AE',
        replace: true,
        scope: {},
        template: $breadcrumb.getTemplate($$templates),
        templateUrl: $breadcrumb.getTemplateUrl(),
        link: {
            post: function postLink(scope) {
                var labelWatchers = [];

                var renderBreadcrumb = function() {
                    deregisterWatchers(labelWatchers);
                    labelWatchers = [];
                    
                    var viewScope = $breadcrumb.$getLastViewScope();
                    scope.steps = $breadcrumb.getStatesChain();
                    angular.forEach(scope.steps, function (step) {
                        if (step.ncyBreadcrumb && step.ncyBreadcrumb.label) {
                            var parseLabel = $interpolate(step.ncyBreadcrumb.label);
                            step.ncyBreadcrumbLabel = parseLabel(viewScope);
                            // Watcher for further viewScope updates
                            registerWatchers(labelWatchers, parseLabel, viewScope, step);
                        } else {
                            step.ncyBreadcrumbLabel = step.name;
                        }
                    });
                };

                $rootScope.$on('$viewContentLoaded', function (event) {
                    if(!event.targetScope.ncyBreadcrumbIgnore) {
                        renderBreadcrumb();
                    }
                });

                // View(s) may be already loaded while the directive's linking
                renderBreadcrumb();
            }
        }
    };
}
BreadcrumbDirective.$inject = ['$interpolate', '$breadcrumb', '$rootScope'];

function BreadcrumbLastDirective($interpolate, $breadcrumb, $rootScope) {

    return {
        restrict: 'A',
        scope: {},
        template: '{{ncyBreadcrumbLabel}}',
        compile: function(cElement, cAttrs) {

            // Override the default template if ncyBreadcrumbLast has a value
            var template = cElement.attr(cAttrs.$attr.ncyBreadcrumbLast);
            if(template) {
                cElement.html(template);
            }

            return {
                post: function postLink(scope) {
                    var labelWatchers = [];

                    var renderLabel = function() {
                        deregisterWatchers(labelWatchers);
                        labelWatchers = [];
                        
                        var viewScope = $breadcrumb.$getLastViewScope();
                        var lastStep = $breadcrumb.getLastStep();
                        if(lastStep) {
                            scope.ncyBreadcrumbLink = lastStep.ncyBreadcrumbLink;
                            if (lastStep.ncyBreadcrumb && lastStep.ncyBreadcrumb.label) {
                                var parseLabel = $interpolate(lastStep.ncyBreadcrumb.label);
                                scope.ncyBreadcrumbLabel = parseLabel(viewScope);
                                // Watcher for further viewScope updates
                                // Tricky last arg: the last step is the entire scope of the directive !
                                registerWatchers(labelWatchers, parseLabel, viewScope, scope);
                            } else {
                                scope.ncyBreadcrumbLabel = lastStep.name;
                            }
                        }
                    };

                    $rootScope.$on('$viewContentLoaded', function (event) {
                        if(!event.targetScope.ncyBreadcrumbIgnore) {
                            renderLabel();
                        }
                    });

                    // View(s) may be already loaded while the directive's linking
                    renderLabel();
                }
            };

        }
    };
}
BreadcrumbLastDirective.$inject = ['$interpolate', '$breadcrumb', '$rootScope'];

function BreadcrumbTextDirective($interpolate, $breadcrumb, $rootScope) {

    return {
        restrict: 'A',
        scope: {},
        template: '{{ncyBreadcrumbChain}}',

        compile: function(cElement, cAttrs) {
            // Override the default template if ncyBreadcrumbText has a value
            var template = cElement.attr(cAttrs.$attr.ncyBreadcrumbText);
            if(template) {
                cElement.html(template);
            }
            
            var separator = cElement.attr(cAttrs.$attr.ncyBreadcrumbTextSeparator) || ' / ';

            return {
                post: function postLink(scope) {
                    var labelWatchers = [];
                    
                    var registerWatchersText = function(labelWatcherArray, interpolationFunction, viewScope) {
                        angular.forEach(getExpression(interpolationFunction), function(expression) {
                            var watcher = viewScope.$watch(expression, function(newValue, oldValue) {
                                if (newValue !== oldValue) {
                                    renderLabel();
                                }
                            });
                            labelWatcherArray.push(watcher);
                        });
                    };

                    var renderLabel = function() {
                        deregisterWatchers(labelWatchers);
                        labelWatchers = [];
                        
                        var viewScope = $breadcrumb.$getLastViewScope();
                        var steps = $breadcrumb.getStatesChain();
                        var combinedLabels = [];
                        angular.forEach(steps, function (step) {
                            if (step.ncyBreadcrumb && step.ncyBreadcrumb.label) {
                                var parseLabel = $interpolate(step.ncyBreadcrumb.label);
                                combinedLabels.push(parseLabel(viewScope));
                                // Watcher for further viewScope updates
                                registerWatchersText(labelWatchers, parseLabel, viewScope);
                            } else {
                                combinedLabels.push(step.name);
                            }
                        });
                        
                        scope.ncyBreadcrumbChain = combinedLabels.join(separator);
                    };

                    $rootScope.$on('$viewContentLoaded', function (event) {
                        if(!event.targetScope.ncyBreadcrumbIgnore) {
                            renderLabel();
                        }
                    });

                    // View(s) may be already loaded while the directive's linking
                    renderLabel();
                }
            };

        }
    };
}
BreadcrumbTextDirective.$inject = ['$interpolate', '$breadcrumb', '$rootScope'];

angular.module('ncy-angular-breadcrumb', ['ui.router.state'])
    .provider('$breadcrumb', $Breadcrumb)
    .directive('ncyBreadcrumb', BreadcrumbDirective)
    .directive('ncyBreadcrumbLast', BreadcrumbLastDirective)
    .directive('ncyBreadcrumbText', BreadcrumbTextDirective);
})(window, window.angular);

/**
 * angular-input-masks
 * Personalized input masks for AngularJS
 * @version v2.6.0
 * @link http://github.com/assisrafael/angular-input-masks
 * @license MIT
 */
require=(function e(t,n,r){function s(o,u){if(!n[o]){if(!t[o]){var a=typeof require=="function"&&require;if(!u&&a)return a(o,!0);if(i)return i(o,!0);var f=new Error("Cannot find module '"+o+"'");throw f.code="MODULE_NOT_FOUND",f}var l=n[o]={exports:{}};t[o][0].call(l.exports,function(e){var n=t[o][1][e];return s(n?n:e)},l,l.exports,e,t,n,r)}return n[o].exports}var i=typeof require=="function"&&require;for(var o=0;o<r.length;o++)s(r[o]);return s})({1:[function(require,module,exports){
/**
 * br-validations
 * A library of validations applicable to several Brazilian data like I.E., CNPJ, CPF and others
 * @version v0.2.4
 * @link http://github.com/the-darc/br-validations
 * @license MIT
 */
(function (root, factory) {
	if (typeof define === 'function' && define.amd) {
		// AMD. Register as an anonymous module.
		define([], factory);
	} else if (typeof exports === 'object') {
		// Node. Does not work with strict CommonJS, but
		// only CommonJS-like environments that support module.exports,
		// like Node.
		module.exports = factory();
	} else {
		// Browser globals (root is window)
		root.BrV = factory();
	}
}(this, function () {
var CNPJ = {};

CNPJ.validate = function(c) {
	var b = [6,5,4,3,2,9,8,7,6,5,4,3,2];
	c = c.replace(/[^\d]/g,'');

	var r = /^(0{14}|1{14}|2{14}|3{14}|4{14}|5{14}|6{14}|7{14}|8{14}|9{14})$/;
	if (!c || c.length !== 14 || r.test(c)) {
		return false;
	}
	c = c.split('');

	for (var i = 0, n = 0; i < 12; i++) {
		n += c[i] * b[i+1];
	}
	n = 11 - n%11;
	n = n >= 10 ? 0 : n;
	if (parseInt(c[12]) !== n)  {
		return false;
	}

	for (i = 0, n = 0; i <= 12; i++) {
		n += c[i] * b[i];
	}
	n = 11 - n%11;
	n = n >= 10 ? 0 : n;
	if (parseInt(c[13]) !== n)  {
		return false;
	}
	return true;
};


var CPF = {};

CPF.validate = function(cpf) {
	cpf = cpf.replace(/[^\d]+/g,'');
	var r = /^(0{11}|1{11}|2{11}|3{11}|4{11}|5{11}|6{11}|7{11}|8{11}|9{11})$/;
	if (!cpf || cpf.length !== 11 || r.test(cpf)) {
		return false;
	}
	function validateDigit(digit) {
		var add = 0;
		var init = digit - 9;
		for (var i = 0; i < 9; i ++) {
			add += parseInt(cpf.charAt(i + init)) * (i+1);
		}
		return (add%11)%10 === parseInt(cpf.charAt(digit));
	}
	return validateDigit(9) && validateDigit(10);
};

var IE = function(uf) {
	if (!(this instanceof IE)) {
		return new IE(uf);
	}

	this.rules = IErules[uf] || [];
	this.rule;
	IE.prototype._defineRule = function(value) {
		this.rule = undefined;
		for (var r = 0; r < this.rules.length && this.rule === undefined; r++) {
			var str = value.replace(/[^\d]/g,'');
			var ruleCandidate = this.rules[r];
			if (str.length === ruleCandidate.chars && (!ruleCandidate.match || ruleCandidate.match.test(value))) {
				this.rule = ruleCandidate;
			}
		}
		return !!this.rule;
	};

	IE.prototype.validate = function(value) {
		if (!value || !this._defineRule(value)) {
			return false;
		}
		return this.rule.validate(value);
	};
};

var IErules = {};

var algorithmSteps = {
	handleStr: {
		onlyNumbers: function(str) {
			return str.replace(/[^\d]/g,'').split('');
		},
		mgSpec: function(str) {
			var s = str.replace(/[^\d]/g,'');
			s = s.substr(0,3)+'0'+s.substr(3, s.length);
			return s.split('');
		}
	},
	sum: {
		normalSum: function(handledStr, pesos) {
			var nums = handledStr;
			var sum = 0;
			for (var i = 0; i < pesos.length; i++) {
				sum += parseInt(nums[i]) * pesos[i];
			}
			return sum;
		},
		individualSum: function(handledStr, pesos) {
			var nums = handledStr;
			var sum = 0;
			for (var i = 0; i < pesos.length; i++) {
				var mult = parseInt(nums[i]) * pesos[i];
				sum += mult%10 + parseInt(mult/10);
			}
			return sum;
		},
		apSpec: function(handledStr, pesos) {
			var sum = this.normalSum(handledStr, pesos);
			var ref = handledStr.join('');
			if (ref >= '030000010' && ref <= '030170009') {
				return sum + 5;
			}
			if (ref >= '030170010' && ref <= '030190229') {
				return sum + 9;
			}
			return sum;
		}
	},
	rest: {
		mod11: function(sum) {
			return sum%11;
		},
		mod10: function(sum) {
			return sum%10;
		},
		mod9: function(sum) {
			return sum%9;
		}
	},
	expectedDV: {
		minusRestOf11: function(rest) {
			return rest < 2 ? 0 : 11 - rest;
		},
		minusRestOf11v2: function(rest) {
			return rest < 2 ? 11 - rest - 10 : 11 - rest;
		},
		minusRestOf10: function(rest) {
			return rest < 1 ? 0 : 10 - rest;
		},
		mod10: function(rest) {
			return rest%10;
		},
		goSpec: function(rest, handledStr) {
			var ref = handledStr.join('');
			if (rest === 1) {
				return ref >= '101031050' && ref <= '101199979' ? 1 : 0;
			}
			return rest === 0 ? 0 : 11 - rest;
		},
		apSpec: function(rest, handledStr) {
			var ref = handledStr.join('');
			if (rest === 0) {
				return ref >= '030170010' && ref <= '030190229' ? 1 : 0;
			}
			return rest === 1 ? 0 : 11 - rest;
		},
		voidFn: function(rest) {
			return rest;
		}
	}
};


/**
 * options {
 *     pesos: Array of values used to operate in sum step
 *     dvPos: Position of the DV to validate considering the handledStr
 *     algorithmSteps: The four DV's validation algorithm steps names
 * }
 */
function validateDV(value, options) {
	var steps = options.algorithmSteps;

	// Step 01: Handle String
	var handledStr = algorithmSteps.handleStr[steps[0]](value);

	// Step 02: Sum chars
	var sum = algorithmSteps.sum[steps[1]](handledStr, options.pesos);

	// Step 03: Rest calculation
	var rest = algorithmSteps.rest[steps[2]](sum);

	// Fixed Step: Get current DV
	var currentDV = parseInt(handledStr[options.dvpos]);

	// Step 04: Expected DV calculation
	var expectedDV = algorithmSteps.expectedDV[steps[3]](rest, handledStr);

	// Fixed step: DV verification
	return currentDV === expectedDV;
}

function validateIE(value, rule) {
	if (rule.match && !rule.match.test(value)) {
		return false;
	}
	for (var i = 0; i < rule.dvs.length; i++) {
		// console.log('>> >> dv'+i);
		if (!validateDV(value, rule.dvs[i])) {
			return false;
		}
	}
	return true;
}

IErules.PE = [{
	//mask: new StringMask('0000000-00'),
	chars: 9,
	dvs: [{
		dvpos: 7,
		pesos: [8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	},{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
},{
	// mask: new StringMask('00.0.000.0000000-0'),
	chars: 14,
	pesos: [[1,2,3,4,5,9,8,7,6,5,4,3,2]],
	dvs: [{
		dvpos: 13,
		pesos: [5,4,3,2,1,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11v2']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.RS = [{
	// mask: new StringMask('000/0000000'),
	chars: 10,
	dvs: [{
		dvpos: 9,
		pesos: [2,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.AC = [{
	// mask: new StringMask('00.000.000/000-00'),
	chars: 13,
	match: /^01/,
	dvs: [{
		dvpos: 11,
		pesos: [4,3,2,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	},{
		dvpos: 12,
		pesos: [5,4,3,2,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.MG = [{
	// mask: new StringMask('000.000.000/0000'),
	chars: 13,
	dvs: [{
		dvpos: 12,
		pesos: [1,2,1,2,1,2,1,2,1,2,1,2],
		algorithmSteps: ['mgSpec', 'individualSum', 'mod10', 'minusRestOf10']
	},{
		dvpos: 12,
		pesos: [3,2,11,10,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.SP = [{
	// mask: new StringMask('000.000.000.000'),
	chars: 12,
	match: /^[0-9]/,
	dvs: [{
		dvpos: 8,
		pesos: [1,3,4,5,6,7,8,10],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'mod10']
	},{
		dvpos: 11,
		pesos: [3,2,10,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'mod10']
	}],
	validate: function(value) { return validateIE(value, this); }
},{
	// mask: new StringMask('P-00000000.0/000')
	chars: 12,
	match: /^P/i,
	dvs: [{
		dvpos: 8,
		pesos: [1,3,4,5,6,7,8,10],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'mod10']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.DF = [{
	// mask: new StringMask('00000000000-00'),
	chars: 13,
	dvs: [{
		dvpos: 11,
		pesos: [4,3,2,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	},{
		dvpos: 12,
		pesos: [5,4,3,2,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.ES = [{
	// mask: new StringMask('000.000.00-0')
	chars: 9,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.BA = [{
	// mask: new StringMask('000000-00')
	chars: 8,
	match: /^[0123458]/,
	dvs: [{
		dvpos: 7,
		pesos: [7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod10', 'minusRestOf10']
	},{
		dvpos: 6,
		pesos: [8,7,6,5,4,3,0,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod10', 'minusRestOf10']
	}],
	validate: function(value) { return validateIE(value, this); }
},{
	chars: 8,
	match: /^[679]/,
	dvs: [{
		dvpos: 7,
		pesos: [7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	},{
		dvpos: 6,
		pesos: [8,7,6,5,4,3,0,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
},{
	// mask: new StringMask('0000000-00')
	chars: 9,
	match: /^[0-9][0123458]/,
	dvs: [{
		dvpos: 8,
		pesos: [8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod10', 'minusRestOf10']
	},{
		dvpos: 7,
		pesos: [9,8,7,6,5,4,3,0,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod10', 'minusRestOf10']
	}],
	validate: function(value) { return validateIE(value, this); }
},{
	chars: 9,
	match: /^[0-9][679]/,
	dvs: [{
		dvpos: 8,
		pesos: [8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	},{
		dvpos: 7,
		pesos: [9,8,7,6,5,4,3,0,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.AM = [{
	//mask: new StringMask('00.000.000-0')
	chars: 9,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.RN = [{
	// {mask: new StringMask('00.000.000-0')
	chars: 9,
	match: /^20/,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
},{
	// {mask: new StringMask('00.0.000.000-0'), chars: 10}
	chars: 10,
	match: /^20/,
	dvs: [{
		dvpos: 8,
		pesos: [10,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.RO = [{
	// mask: new StringMask('0000000000000-0')
	chars: 14,
	dvs: [{
		dvpos: 13,
		pesos: [6,5,4,3,2,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.PR = [{
	// mask: new StringMask('00000000-00')
	chars: 10,
	dvs: [{
		dvpos: 8,
		pesos: [3,2,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	},{
		dvpos: 9,
		pesos: [4,3,2,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.SC = [{
	// {mask: new StringMask('000.000.000'), uf: 'SANTA CATARINA'}
	chars: 9,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.RJ = [{
	// {mask: new StringMask('00.000.00-0'), uf: 'RIO DE JANEIRO'}
	chars: 8,
	dvs: [{
		dvpos: 7,
		pesos: [2,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.PA = [{
	// {mask: new StringMask('00-000000-0')
	chars: 9,
	match: /^15/,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.SE = [{
	// {mask: new StringMask('00000000-0')
	chars: 9,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.PB = [{
	// {mask: new StringMask('00000000-0')
	chars: 9,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.CE = [{
	// {mask: new StringMask('00000000-0')
	chars: 9,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.PI = [{
	// {mask: new StringMask('000000000')
	chars: 9,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.MA = [{
	// {mask: new StringMask('000000000')
	chars: 9,
	match: /^12/,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.MT = [{
	// {mask: new StringMask('0000000000-0')
	chars: 11,
	dvs: [{
		dvpos: 10,
		pesos: [3,2,9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.MS = [{
	// {mask: new StringMask('000000000')
	chars: 9,
	match: /^28/,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.TO = [{
	// {mask: new StringMask('00000000000'),
	chars: 11,
	match: /^[0-9]{2}((0[123])|(99))/,
	dvs: [{
		dvpos: 10,
		pesos: [9,8,0,0,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.AL = [{
	// {mask: new StringMask('000000000')
	chars: 9,
	match: /^24[03578]/,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'minusRestOf11']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.RR = [{
	// {mask: new StringMask('00000000-0')
	chars: 9,
	match: /^24/,
	dvs: [{
		dvpos: 8,
		pesos: [1,2,3,4,5,6,7,8],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod9', 'voidFn']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.GO = [{
	// {mask: new StringMask('00.000.000-0')
	chars: 9,
	match: /^1[015]/,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'normalSum', 'mod11', 'goSpec']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

IErules.AP = [{
	// {mask: new StringMask('000000000')
	chars: 9,
	match: /^03/,
	dvs: [{
		dvpos: 8,
		pesos: [9,8,7,6,5,4,3,2],
		algorithmSteps: ['onlyNumbers', 'apSpec', 'mod11', 'apSpec']
	}],
	validate: function(value) { return validateIE(value, this); }
}];

	return {
		ie: IE,
		cpf: CPF,
		cnpj: CNPJ
	};
}));
},{}],2:[function(require,module,exports){
//! moment.js
//! version : 2.14.1
//! authors : Tim Wood, Iskren Chernev, Moment.js contributors
//! license : MIT
//! momentjs.com

;(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
    typeof define === 'function' && define.amd ? define(factory) :
    global.moment = factory()
}(this, function () { 'use strict';

    var hookCallback;

    function utils_hooks__hooks () {
        return hookCallback.apply(null, arguments);
    }

    // This is done to register the method called with moment()
    // without creating circular dependencies.
    function setHookCallback (callback) {
        hookCallback = callback;
    }

    function isArray(input) {
        return input instanceof Array || Object.prototype.toString.call(input) === '[object Array]';
    }

    function isObject(input) {
        return Object.prototype.toString.call(input) === '[object Object]';
    }

    function isObjectEmpty(obj) {
        var k;
        for (k in obj) {
            // even if its not own property I'd still call it non-empty
            return false;
        }
        return true;
    }

    function isDate(input) {
        return input instanceof Date || Object.prototype.toString.call(input) === '[object Date]';
    }

    function map(arr, fn) {
        var res = [], i;
        for (i = 0; i < arr.length; ++i) {
            res.push(fn(arr[i], i));
        }
        return res;
    }

    function hasOwnProp(a, b) {
        return Object.prototype.hasOwnProperty.call(a, b);
    }

    function extend(a, b) {
        for (var i in b) {
            if (hasOwnProp(b, i)) {
                a[i] = b[i];
            }
        }

        if (hasOwnProp(b, 'toString')) {
            a.toString = b.toString;
        }

        if (hasOwnProp(b, 'valueOf')) {
            a.valueOf = b.valueOf;
        }

        return a;
    }

    function create_utc__createUTC (input, format, locale, strict) {
        return createLocalOrUTC(input, format, locale, strict, true).utc();
    }

    function defaultParsingFlags() {
        // We need to deep clone this object.
        return {
            empty           : false,
            unusedTokens    : [],
            unusedInput     : [],
            overflow        : -2,
            charsLeftOver   : 0,
            nullInput       : false,
            invalidMonth    : null,
            invalidFormat   : false,
            userInvalidated : false,
            iso             : false,
            parsedDateParts : [],
            meridiem        : null
        };
    }

    function getParsingFlags(m) {
        if (m._pf == null) {
            m._pf = defaultParsingFlags();
        }
        return m._pf;
    }

    var some;
    if (Array.prototype.some) {
        some = Array.prototype.some;
    } else {
        some = function (fun) {
            var t = Object(this);
            var len = t.length >>> 0;

            for (var i = 0; i < len; i++) {
                if (i in t && fun.call(this, t[i], i, t)) {
                    return true;
                }
            }

            return false;
        };
    }

    function valid__isValid(m) {
        if (m._isValid == null) {
            var flags = getParsingFlags(m);
            var parsedParts = some.call(flags.parsedDateParts, function (i) {
                return i != null;
            });
            m._isValid = !isNaN(m._d.getTime()) &&
                flags.overflow < 0 &&
                !flags.empty &&
                !flags.invalidMonth &&
                !flags.invalidWeekday &&
                !flags.nullInput &&
                !flags.invalidFormat &&
                !flags.userInvalidated &&
                (!flags.meridiem || (flags.meridiem && parsedParts));

            if (m._strict) {
                m._isValid = m._isValid &&
                    flags.charsLeftOver === 0 &&
                    flags.unusedTokens.length === 0 &&
                    flags.bigHour === undefined;
            }
        }
        return m._isValid;
    }

    function valid__createInvalid (flags) {
        var m = create_utc__createUTC(NaN);
        if (flags != null) {
            extend(getParsingFlags(m), flags);
        }
        else {
            getParsingFlags(m).userInvalidated = true;
        }

        return m;
    }

    function isUndefined(input) {
        return input === void 0;
    }

    // Plugins that add properties should also add the key here (null value),
    // so we can properly clone ourselves.
    var momentProperties = utils_hooks__hooks.momentProperties = [];

    function copyConfig(to, from) {
        var i, prop, val;

        if (!isUndefined(from._isAMomentObject)) {
            to._isAMomentObject = from._isAMomentObject;
        }
        if (!isUndefined(from._i)) {
            to._i = from._i;
        }
        if (!isUndefined(from._f)) {
            to._f = from._f;
        }
        if (!isUndefined(from._l)) {
            to._l = from._l;
        }
        if (!isUndefined(from._strict)) {
            to._strict = from._strict;
        }
        if (!isUndefined(from._tzm)) {
            to._tzm = from._tzm;
        }
        if (!isUndefined(from._isUTC)) {
            to._isUTC = from._isUTC;
        }
        if (!isUndefined(from._offset)) {
            to._offset = from._offset;
        }
        if (!isUndefined(from._pf)) {
            to._pf = getParsingFlags(from);
        }
        if (!isUndefined(from._locale)) {
            to._locale = from._locale;
        }

        if (momentProperties.length > 0) {
            for (i in momentProperties) {
                prop = momentProperties[i];
                val = from[prop];
                if (!isUndefined(val)) {
                    to[prop] = val;
                }
            }
        }

        return to;
    }

    var updateInProgress = false;

    // Moment prototype object
    function Moment(config) {
        copyConfig(this, config);
        this._d = new Date(config._d != null ? config._d.getTime() : NaN);
        // Prevent infinite loop in case updateOffset creates new moment
        // objects.
        if (updateInProgress === false) {
            updateInProgress = true;
            utils_hooks__hooks.updateOffset(this);
            updateInProgress = false;
        }
    }

    function isMoment (obj) {
        return obj instanceof Moment || (obj != null && obj._isAMomentObject != null);
    }

    function absFloor (number) {
        if (number < 0) {
            // -0 -> 0
            return Math.ceil(number) || 0;
        } else {
            return Math.floor(number);
        }
    }

    function toInt(argumentForCoercion) {
        var coercedNumber = +argumentForCoercion,
            value = 0;

        if (coercedNumber !== 0 && isFinite(coercedNumber)) {
            value = absFloor(coercedNumber);
        }

        return value;
    }

    // compare two arrays, return the number of differences
    function compareArrays(array1, array2, dontConvert) {
        var len = Math.min(array1.length, array2.length),
            lengthDiff = Math.abs(array1.length - array2.length),
            diffs = 0,
            i;
        for (i = 0; i < len; i++) {
            if ((dontConvert && array1[i] !== array2[i]) ||
                (!dontConvert && toInt(array1[i]) !== toInt(array2[i]))) {
                diffs++;
            }
        }
        return diffs + lengthDiff;
    }

    function warn(msg) {
        if (utils_hooks__hooks.suppressDeprecationWarnings === false &&
                (typeof console !==  'undefined') && console.warn) {
            console.warn('Deprecation warning: ' + msg);
        }
    }

    function deprecate(msg, fn) {
        var firstTime = true;

        return extend(function () {
            if (utils_hooks__hooks.deprecationHandler != null) {
                utils_hooks__hooks.deprecationHandler(null, msg);
            }
            if (firstTime) {
                warn(msg + '\nArguments: ' + Array.prototype.slice.call(arguments).join(', ') + '\n' + (new Error()).stack);
                firstTime = false;
            }
            return fn.apply(this, arguments);
        }, fn);
    }

    var deprecations = {};

    function deprecateSimple(name, msg) {
        if (utils_hooks__hooks.deprecationHandler != null) {
            utils_hooks__hooks.deprecationHandler(name, msg);
        }
        if (!deprecations[name]) {
            warn(msg);
            deprecations[name] = true;
        }
    }

    utils_hooks__hooks.suppressDeprecationWarnings = false;
    utils_hooks__hooks.deprecationHandler = null;

    function isFunction(input) {
        return input instanceof Function || Object.prototype.toString.call(input) === '[object Function]';
    }

    function locale_set__set (config) {
        var prop, i;
        for (i in config) {
            prop = config[i];
            if (isFunction(prop)) {
                this[i] = prop;
            } else {
                this['_' + i] = prop;
            }
        }
        this._config = config;
        // Lenient ordinal parsing accepts just a number in addition to
        // number + (possibly) stuff coming from _ordinalParseLenient.
        this._ordinalParseLenient = new RegExp(this._ordinalParse.source + '|' + (/\d{1,2}/).source);
    }

    function mergeConfigs(parentConfig, childConfig) {
        var res = extend({}, parentConfig), prop;
        for (prop in childConfig) {
            if (hasOwnProp(childConfig, prop)) {
                if (isObject(parentConfig[prop]) && isObject(childConfig[prop])) {
                    res[prop] = {};
                    extend(res[prop], parentConfig[prop]);
                    extend(res[prop], childConfig[prop]);
                } else if (childConfig[prop] != null) {
                    res[prop] = childConfig[prop];
                } else {
                    delete res[prop];
                }
            }
        }
        for (prop in parentConfig) {
            if (hasOwnProp(parentConfig, prop) &&
                    !hasOwnProp(childConfig, prop) &&
                    isObject(parentConfig[prop])) {
                // make sure changes to properties don't modify parent config
                res[prop] = extend({}, res[prop]);
            }
        }
        return res;
    }

    function Locale(config) {
        if (config != null) {
            this.set(config);
        }
    }

    var keys;

    if (Object.keys) {
        keys = Object.keys;
    } else {
        keys = function (obj) {
            var i, res = [];
            for (i in obj) {
                if (hasOwnProp(obj, i)) {
                    res.push(i);
                }
            }
            return res;
        };
    }

    var defaultCalendar = {
        sameDay : '[Today at] LT',
        nextDay : '[Tomorrow at] LT',
        nextWeek : 'dddd [at] LT',
        lastDay : '[Yesterday at] LT',
        lastWeek : '[Last] dddd [at] LT',
        sameElse : 'L'
    };

    function locale_calendar__calendar (key, mom, now) {
        var output = this._calendar[key] || this._calendar['sameElse'];
        return isFunction(output) ? output.call(mom, now) : output;
    }

    var defaultLongDateFormat = {
        LTS  : 'h:mm:ss A',
        LT   : 'h:mm A',
        L    : 'MM/DD/YYYY',
        LL   : 'MMMM D, YYYY',
        LLL  : 'MMMM D, YYYY h:mm A',
        LLLL : 'dddd, MMMM D, YYYY h:mm A'
    };

    function longDateFormat (key) {
        var format = this._longDateFormat[key],
            formatUpper = this._longDateFormat[key.toUpperCase()];

        if (format || !formatUpper) {
            return format;
        }

        this._longDateFormat[key] = formatUpper.replace(/MMMM|MM|DD|dddd/g, function (val) {
            return val.slice(1);
        });

        return this._longDateFormat[key];
    }

    var defaultInvalidDate = 'Invalid date';

    function invalidDate () {
        return this._invalidDate;
    }

    var defaultOrdinal = '%d';
    var defaultOrdinalParse = /\d{1,2}/;

    function ordinal (number) {
        return this._ordinal.replace('%d', number);
    }

    var defaultRelativeTime = {
        future : 'in %s',
        past   : '%s ago',
        s  : 'a few seconds',
        m  : 'a minute',
        mm : '%d minutes',
        h  : 'an hour',
        hh : '%d hours',
        d  : 'a day',
        dd : '%d days',
        M  : 'a month',
        MM : '%d months',
        y  : 'a year',
        yy : '%d years'
    };

    function relative__relativeTime (number, withoutSuffix, string, isFuture) {
        var output = this._relativeTime[string];
        return (isFunction(output)) ?
            output(number, withoutSuffix, string, isFuture) :
            output.replace(/%d/i, number);
    }

    function pastFuture (diff, output) {
        var format = this._relativeTime[diff > 0 ? 'future' : 'past'];
        return isFunction(format) ? format(output) : format.replace(/%s/i, output);
    }

    var aliases = {};

    function addUnitAlias (unit, shorthand) {
        var lowerCase = unit.toLowerCase();
        aliases[lowerCase] = aliases[lowerCase + 's'] = aliases[shorthand] = unit;
    }

    function normalizeUnits(units) {
        return typeof units === 'string' ? aliases[units] || aliases[units.toLowerCase()] : undefined;
    }

    function normalizeObjectUnits(inputObject) {
        var normalizedInput = {},
            normalizedProp,
            prop;

        for (prop in inputObject) {
            if (hasOwnProp(inputObject, prop)) {
                normalizedProp = normalizeUnits(prop);
                if (normalizedProp) {
                    normalizedInput[normalizedProp] = inputObject[prop];
                }
            }
        }

        return normalizedInput;
    }

    var priorities = {};

    function addUnitPriority(unit, priority) {
        priorities[unit] = priority;
    }

    function getPrioritizedUnits(unitsObj) {
        var units = [];
        for (var u in unitsObj) {
            units.push({unit: u, priority: priorities[u]});
        }
        units.sort(function (a, b) {
            return a.priority - b.priority;
        });
        return units;
    }

    function makeGetSet (unit, keepTime) {
        return function (value) {
            if (value != null) {
                get_set__set(this, unit, value);
                utils_hooks__hooks.updateOffset(this, keepTime);
                return this;
            } else {
                return get_set__get(this, unit);
            }
        };
    }

    function get_set__get (mom, unit) {
        return mom.isValid() ?
            mom._d['get' + (mom._isUTC ? 'UTC' : '') + unit]() : NaN;
    }

    function get_set__set (mom, unit, value) {
        if (mom.isValid()) {
            mom._d['set' + (mom._isUTC ? 'UTC' : '') + unit](value);
        }
    }

    // MOMENTS

    function stringGet (units) {
        units = normalizeUnits(units);
        if (isFunction(this[units])) {
            return this[units]();
        }
        return this;
    }


    function stringSet (units, value) {
        if (typeof units === 'object') {
            units = normalizeObjectUnits(units);
            var prioritized = getPrioritizedUnits(units);
            for (var i = 0; i < prioritized.length; i++) {
                this[prioritized[i].unit](units[prioritized[i].unit]);
            }
        } else {
            units = normalizeUnits(units);
            if (isFunction(this[units])) {
                return this[units](value);
            }
        }
        return this;
    }

    function zeroFill(number, targetLength, forceSign) {
        var absNumber = '' + Math.abs(number),
            zerosToFill = targetLength - absNumber.length,
            sign = number >= 0;
        return (sign ? (forceSign ? '+' : '') : '-') +
            Math.pow(10, Math.max(0, zerosToFill)).toString().substr(1) + absNumber;
    }

    var formattingTokens = /(\[[^\[]*\])|(\\)?([Hh]mm(ss)?|Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Qo?|YYYYYY|YYYYY|YYYY|YY|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|kk?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g;

    var localFormattingTokens = /(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g;

    var formatFunctions = {};

    var formatTokenFunctions = {};

    // token:    'M'
    // padded:   ['MM', 2]
    // ordinal:  'Mo'
    // callback: function () { this.month() + 1 }
    function addFormatToken (token, padded, ordinal, callback) {
        var func = callback;
        if (typeof callback === 'string') {
            func = function () {
                return this[callback]();
            };
        }
        if (token) {
            formatTokenFunctions[token] = func;
        }
        if (padded) {
            formatTokenFunctions[padded[0]] = function () {
                return zeroFill(func.apply(this, arguments), padded[1], padded[2]);
            };
        }
        if (ordinal) {
            formatTokenFunctions[ordinal] = function () {
                return this.localeData().ordinal(func.apply(this, arguments), token);
            };
        }
    }

    function removeFormattingTokens(input) {
        if (input.match(/\[[\s\S]/)) {
            return input.replace(/^\[|\]$/g, '');
        }
        return input.replace(/\\/g, '');
    }

    function makeFormatFunction(format) {
        var array = format.match(formattingTokens), i, length;

        for (i = 0, length = array.length; i < length; i++) {
            if (formatTokenFunctions[array[i]]) {
                array[i] = formatTokenFunctions[array[i]];
            } else {
                array[i] = removeFormattingTokens(array[i]);
            }
        }

        return function (mom) {
            var output = '', i;
            for (i = 0; i < length; i++) {
                output += array[i] instanceof Function ? array[i].call(mom, format) : array[i];
            }
            return output;
        };
    }

    // format date using native date object
    function formatMoment(m, format) {
        if (!m.isValid()) {
            return m.localeData().invalidDate();
        }

        format = expandFormat(format, m.localeData());
        formatFunctions[format] = formatFunctions[format] || makeFormatFunction(format);

        return formatFunctions[format](m);
    }

    function expandFormat(format, locale) {
        var i = 5;

        function replaceLongDateFormatTokens(input) {
            return locale.longDateFormat(input) || input;
        }

        localFormattingTokens.lastIndex = 0;
        while (i >= 0 && localFormattingTokens.test(format)) {
            format = format.replace(localFormattingTokens, replaceLongDateFormatTokens);
            localFormattingTokens.lastIndex = 0;
            i -= 1;
        }

        return format;
    }

    var match1         = /\d/;            //       0 - 9
    var match2         = /\d\d/;          //      00 - 99
    var match3         = /\d{3}/;         //     000 - 999
    var match4         = /\d{4}/;         //    0000 - 9999
    var match6         = /[+-]?\d{6}/;    // -999999 - 999999
    var match1to2      = /\d\d?/;         //       0 - 99
    var match3to4      = /\d\d\d\d?/;     //     999 - 9999
    var match5to6      = /\d\d\d\d\d\d?/; //   99999 - 999999
    var match1to3      = /\d{1,3}/;       //       0 - 999
    var match1to4      = /\d{1,4}/;       //       0 - 9999
    var match1to6      = /[+-]?\d{1,6}/;  // -999999 - 999999

    var matchUnsigned  = /\d+/;           //       0 - inf
    var matchSigned    = /[+-]?\d+/;      //    -inf - inf

    var matchOffset    = /Z|[+-]\d\d:?\d\d/gi; // +00:00 -00:00 +0000 -0000 or Z
    var matchShortOffset = /Z|[+-]\d\d(?::?\d\d)?/gi; // +00 -00 +00:00 -00:00 +0000 -0000 or Z

    var matchTimestamp = /[+-]?\d+(\.\d{1,3})?/; // 123456789 123456789.123

    // any word (or two) characters or numbers including two/three word month in arabic.
    // includes scottish gaelic two word and hyphenated months
    var matchWord = /[0-9]*['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+|[\u0600-\u06FF\/]+(\s*?[\u0600-\u06FF]+){1,2}/i;


    var regexes = {};

    function addRegexToken (token, regex, strictRegex) {
        regexes[token] = isFunction(regex) ? regex : function (isStrict, localeData) {
            return (isStrict && strictRegex) ? strictRegex : regex;
        };
    }

    function getParseRegexForToken (token, config) {
        if (!hasOwnProp(regexes, token)) {
            return new RegExp(unescapeFormat(token));
        }

        return regexes[token](config._strict, config._locale);
    }

    // Code from http://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript
    function unescapeFormat(s) {
        return regexEscape(s.replace('\\', '').replace(/\\(\[)|\\(\])|\[([^\]\[]*)\]|\\(.)/g, function (matched, p1, p2, p3, p4) {
            return p1 || p2 || p3 || p4;
        }));
    }

    function regexEscape(s) {
        return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    }

    var tokens = {};

    function addParseToken (token, callback) {
        var i, func = callback;
        if (typeof token === 'string') {
            token = [token];
        }
        if (typeof callback === 'number') {
            func = function (input, array) {
                array[callback] = toInt(input);
            };
        }
        for (i = 0; i < token.length; i++) {
            tokens[token[i]] = func;
        }
    }

    function addWeekParseToken (token, callback) {
        addParseToken(token, function (input, array, config, token) {
            config._w = config._w || {};
            callback(input, config._w, config, token);
        });
    }

    function addTimeToArrayFromToken(token, input, config) {
        if (input != null && hasOwnProp(tokens, token)) {
            tokens[token](input, config._a, config, token);
        }
    }

    var YEAR = 0;
    var MONTH = 1;
    var DATE = 2;
    var HOUR = 3;
    var MINUTE = 4;
    var SECOND = 5;
    var MILLISECOND = 6;
    var WEEK = 7;
    var WEEKDAY = 8;

    var indexOf;

    if (Array.prototype.indexOf) {
        indexOf = Array.prototype.indexOf;
    } else {
        indexOf = function (o) {
            // I know
            var i;
            for (i = 0; i < this.length; ++i) {
                if (this[i] === o) {
                    return i;
                }
            }
            return -1;
        };
    }

    function daysInMonth(year, month) {
        return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    }

    // FORMATTING

    addFormatToken('M', ['MM', 2], 'Mo', function () {
        return this.month() + 1;
    });

    addFormatToken('MMM', 0, 0, function (format) {
        return this.localeData().monthsShort(this, format);
    });

    addFormatToken('MMMM', 0, 0, function (format) {
        return this.localeData().months(this, format);
    });

    // ALIASES

    addUnitAlias('month', 'M');

    // PRIORITY

    addUnitPriority('month', 8);

    // PARSING

    addRegexToken('M',    match1to2);
    addRegexToken('MM',   match1to2, match2);
    addRegexToken('MMM',  function (isStrict, locale) {
        return locale.monthsShortRegex(isStrict);
    });
    addRegexToken('MMMM', function (isStrict, locale) {
        return locale.monthsRegex(isStrict);
    });

    addParseToken(['M', 'MM'], function (input, array) {
        array[MONTH] = toInt(input) - 1;
    });

    addParseToken(['MMM', 'MMMM'], function (input, array, config, token) {
        var month = config._locale.monthsParse(input, token, config._strict);
        // if we didn't find a month name, mark the date as invalid.
        if (month != null) {
            array[MONTH] = month;
        } else {
            getParsingFlags(config).invalidMonth = input;
        }
    });

    // LOCALES

    var MONTHS_IN_FORMAT = /D[oD]?(\[[^\[\]]*\]|\s+)+MMMM?/;
    var defaultLocaleMonths = 'January_February_March_April_May_June_July_August_September_October_November_December'.split('_');
    function localeMonths (m, format) {
        return isArray(this._months) ? this._months[m.month()] :
            this._months[(this._months.isFormat || MONTHS_IN_FORMAT).test(format) ? 'format' : 'standalone'][m.month()];
    }

    var defaultLocaleMonthsShort = 'Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec'.split('_');
    function localeMonthsShort (m, format) {
        return isArray(this._monthsShort) ? this._monthsShort[m.month()] :
            this._monthsShort[MONTHS_IN_FORMAT.test(format) ? 'format' : 'standalone'][m.month()];
    }

    function units_month__handleStrictParse(monthName, format, strict) {
        var i, ii, mom, llc = monthName.toLocaleLowerCase();
        if (!this._monthsParse) {
            // this is not used
            this._monthsParse = [];
            this._longMonthsParse = [];
            this._shortMonthsParse = [];
            for (i = 0; i < 12; ++i) {
                mom = create_utc__createUTC([2000, i]);
                this._shortMonthsParse[i] = this.monthsShort(mom, '').toLocaleLowerCase();
                this._longMonthsParse[i] = this.months(mom, '').toLocaleLowerCase();
            }
        }

        if (strict) {
            if (format === 'MMM') {
                ii = indexOf.call(this._shortMonthsParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._longMonthsParse, llc);
                return ii !== -1 ? ii : null;
            }
        } else {
            if (format === 'MMM') {
                ii = indexOf.call(this._shortMonthsParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._longMonthsParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._longMonthsParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._shortMonthsParse, llc);
                return ii !== -1 ? ii : null;
            }
        }
    }

    function localeMonthsParse (monthName, format, strict) {
        var i, mom, regex;

        if (this._monthsParseExact) {
            return units_month__handleStrictParse.call(this, monthName, format, strict);
        }

        if (!this._monthsParse) {
            this._monthsParse = [];
            this._longMonthsParse = [];
            this._shortMonthsParse = [];
        }

        // TODO: add sorting
        // Sorting makes sure if one month (or abbr) is a prefix of another
        // see sorting in computeMonthsParse
        for (i = 0; i < 12; i++) {
            // make the regex if we don't have it already
            mom = create_utc__createUTC([2000, i]);
            if (strict && !this._longMonthsParse[i]) {
                this._longMonthsParse[i] = new RegExp('^' + this.months(mom, '').replace('.', '') + '$', 'i');
                this._shortMonthsParse[i] = new RegExp('^' + this.monthsShort(mom, '').replace('.', '') + '$', 'i');
            }
            if (!strict && !this._monthsParse[i]) {
                regex = '^' + this.months(mom, '') + '|^' + this.monthsShort(mom, '');
                this._monthsParse[i] = new RegExp(regex.replace('.', ''), 'i');
            }
            // test the regex
            if (strict && format === 'MMMM' && this._longMonthsParse[i].test(monthName)) {
                return i;
            } else if (strict && format === 'MMM' && this._shortMonthsParse[i].test(monthName)) {
                return i;
            } else if (!strict && this._monthsParse[i].test(monthName)) {
                return i;
            }
        }
    }

    // MOMENTS

    function setMonth (mom, value) {
        var dayOfMonth;

        if (!mom.isValid()) {
            // No op
            return mom;
        }

        if (typeof value === 'string') {
            if (/^\d+$/.test(value)) {
                value = toInt(value);
            } else {
                value = mom.localeData().monthsParse(value);
                // TODO: Another silent failure?
                if (typeof value !== 'number') {
                    return mom;
                }
            }
        }

        dayOfMonth = Math.min(mom.date(), daysInMonth(mom.year(), value));
        mom._d['set' + (mom._isUTC ? 'UTC' : '') + 'Month'](value, dayOfMonth);
        return mom;
    }

    function getSetMonth (value) {
        if (value != null) {
            setMonth(this, value);
            utils_hooks__hooks.updateOffset(this, true);
            return this;
        } else {
            return get_set__get(this, 'Month');
        }
    }

    function getDaysInMonth () {
        return daysInMonth(this.year(), this.month());
    }

    var defaultMonthsShortRegex = matchWord;
    function monthsShortRegex (isStrict) {
        if (this._monthsParseExact) {
            if (!hasOwnProp(this, '_monthsRegex')) {
                computeMonthsParse.call(this);
            }
            if (isStrict) {
                return this._monthsShortStrictRegex;
            } else {
                return this._monthsShortRegex;
            }
        } else {
            if (!hasOwnProp(this, '_monthsShortRegex')) {
                this._monthsShortRegex = defaultMonthsShortRegex;
            }
            return this._monthsShortStrictRegex && isStrict ?
                this._monthsShortStrictRegex : this._monthsShortRegex;
        }
    }

    var defaultMonthsRegex = matchWord;
    function monthsRegex (isStrict) {
        if (this._monthsParseExact) {
            if (!hasOwnProp(this, '_monthsRegex')) {
                computeMonthsParse.call(this);
            }
            if (isStrict) {
                return this._monthsStrictRegex;
            } else {
                return this._monthsRegex;
            }
        } else {
            if (!hasOwnProp(this, '_monthsRegex')) {
                this._monthsRegex = defaultMonthsRegex;
            }
            return this._monthsStrictRegex && isStrict ?
                this._monthsStrictRegex : this._monthsRegex;
        }
    }

    function computeMonthsParse () {
        function cmpLenRev(a, b) {
            return b.length - a.length;
        }

        var shortPieces = [], longPieces = [], mixedPieces = [],
            i, mom;
        for (i = 0; i < 12; i++) {
            // make the regex if we don't have it already
            mom = create_utc__createUTC([2000, i]);
            shortPieces.push(this.monthsShort(mom, ''));
            longPieces.push(this.months(mom, ''));
            mixedPieces.push(this.months(mom, ''));
            mixedPieces.push(this.monthsShort(mom, ''));
        }
        // Sorting makes sure if one month (or abbr) is a prefix of another it
        // will match the longer piece.
        shortPieces.sort(cmpLenRev);
        longPieces.sort(cmpLenRev);
        mixedPieces.sort(cmpLenRev);
        for (i = 0; i < 12; i++) {
            shortPieces[i] = regexEscape(shortPieces[i]);
            longPieces[i] = regexEscape(longPieces[i]);
        }
        for (i = 0; i < 24; i++) {
            mixedPieces[i] = regexEscape(mixedPieces[i]);
        }

        this._monthsRegex = new RegExp('^(' + mixedPieces.join('|') + ')', 'i');
        this._monthsShortRegex = this._monthsRegex;
        this._monthsStrictRegex = new RegExp('^(' + longPieces.join('|') + ')', 'i');
        this._monthsShortStrictRegex = new RegExp('^(' + shortPieces.join('|') + ')', 'i');
    }

    // FORMATTING

    addFormatToken('Y', 0, 0, function () {
        var y = this.year();
        return y <= 9999 ? '' + y : '+' + y;
    });

    addFormatToken(0, ['YY', 2], 0, function () {
        return this.year() % 100;
    });

    addFormatToken(0, ['YYYY',   4],       0, 'year');
    addFormatToken(0, ['YYYYY',  5],       0, 'year');
    addFormatToken(0, ['YYYYYY', 6, true], 0, 'year');

    // ALIASES

    addUnitAlias('year', 'y');

    // PRIORITIES

    addUnitPriority('year', 1);

    // PARSING

    addRegexToken('Y',      matchSigned);
    addRegexToken('YY',     match1to2, match2);
    addRegexToken('YYYY',   match1to4, match4);
    addRegexToken('YYYYY',  match1to6, match6);
    addRegexToken('YYYYYY', match1to6, match6);

    addParseToken(['YYYYY', 'YYYYYY'], YEAR);
    addParseToken('YYYY', function (input, array) {
        array[YEAR] = input.length === 2 ? utils_hooks__hooks.parseTwoDigitYear(input) : toInt(input);
    });
    addParseToken('YY', function (input, array) {
        array[YEAR] = utils_hooks__hooks.parseTwoDigitYear(input);
    });
    addParseToken('Y', function (input, array) {
        array[YEAR] = parseInt(input, 10);
    });

    // HELPERS

    function daysInYear(year) {
        return isLeapYear(year) ? 366 : 365;
    }

    function isLeapYear(year) {
        return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    }

    // HOOKS

    utils_hooks__hooks.parseTwoDigitYear = function (input) {
        return toInt(input) + (toInt(input) > 68 ? 1900 : 2000);
    };

    // MOMENTS

    var getSetYear = makeGetSet('FullYear', true);

    function getIsLeapYear () {
        return isLeapYear(this.year());
    }

    function createDate (y, m, d, h, M, s, ms) {
        //can't just apply() to create a date:
        //http://stackoverflow.com/questions/181348/instantiating-a-javascript-object-by-calling-prototype-constructor-apply
        var date = new Date(y, m, d, h, M, s, ms);

        //the date constructor remaps years 0-99 to 1900-1999
        if (y < 100 && y >= 0 && isFinite(date.getFullYear())) {
            date.setFullYear(y);
        }
        return date;
    }

    function createUTCDate (y) {
        var date = new Date(Date.UTC.apply(null, arguments));

        //the Date.UTC function remaps years 0-99 to 1900-1999
        if (y < 100 && y >= 0 && isFinite(date.getUTCFullYear())) {
            date.setUTCFullYear(y);
        }
        return date;
    }

    // start-of-first-week - start-of-year
    function firstWeekOffset(year, dow, doy) {
        var // first-week day -- which january is always in the first week (4 for iso, 1 for other)
            fwd = 7 + dow - doy,
            // first-week day local weekday -- which local weekday is fwd
            fwdlw = (7 + createUTCDate(year, 0, fwd).getUTCDay() - dow) % 7;

        return -fwdlw + fwd - 1;
    }

    //http://en.wikipedia.org/wiki/ISO_week_date#Calculating_a_date_given_the_year.2C_week_number_and_weekday
    function dayOfYearFromWeeks(year, week, weekday, dow, doy) {
        var localWeekday = (7 + weekday - dow) % 7,
            weekOffset = firstWeekOffset(year, dow, doy),
            dayOfYear = 1 + 7 * (week - 1) + localWeekday + weekOffset,
            resYear, resDayOfYear;

        if (dayOfYear <= 0) {
            resYear = year - 1;
            resDayOfYear = daysInYear(resYear) + dayOfYear;
        } else if (dayOfYear > daysInYear(year)) {
            resYear = year + 1;
            resDayOfYear = dayOfYear - daysInYear(year);
        } else {
            resYear = year;
            resDayOfYear = dayOfYear;
        }

        return {
            year: resYear,
            dayOfYear: resDayOfYear
        };
    }

    function weekOfYear(mom, dow, doy) {
        var weekOffset = firstWeekOffset(mom.year(), dow, doy),
            week = Math.floor((mom.dayOfYear() - weekOffset - 1) / 7) + 1,
            resWeek, resYear;

        if (week < 1) {
            resYear = mom.year() - 1;
            resWeek = week + weeksInYear(resYear, dow, doy);
        } else if (week > weeksInYear(mom.year(), dow, doy)) {
            resWeek = week - weeksInYear(mom.year(), dow, doy);
            resYear = mom.year() + 1;
        } else {
            resYear = mom.year();
            resWeek = week;
        }

        return {
            week: resWeek,
            year: resYear
        };
    }

    function weeksInYear(year, dow, doy) {
        var weekOffset = firstWeekOffset(year, dow, doy),
            weekOffsetNext = firstWeekOffset(year + 1, dow, doy);
        return (daysInYear(year) - weekOffset + weekOffsetNext) / 7;
    }

    // FORMATTING

    addFormatToken('w', ['ww', 2], 'wo', 'week');
    addFormatToken('W', ['WW', 2], 'Wo', 'isoWeek');

    // ALIASES

    addUnitAlias('week', 'w');
    addUnitAlias('isoWeek', 'W');

    // PRIORITIES

    addUnitPriority('week', 5);
    addUnitPriority('isoWeek', 5);

    // PARSING

    addRegexToken('w',  match1to2);
    addRegexToken('ww', match1to2, match2);
    addRegexToken('W',  match1to2);
    addRegexToken('WW', match1to2, match2);

    addWeekParseToken(['w', 'ww', 'W', 'WW'], function (input, week, config, token) {
        week[token.substr(0, 1)] = toInt(input);
    });

    // HELPERS

    // LOCALES

    function localeWeek (mom) {
        return weekOfYear(mom, this._week.dow, this._week.doy).week;
    }

    var defaultLocaleWeek = {
        dow : 0, // Sunday is the first day of the week.
        doy : 6  // The week that contains Jan 1st is the first week of the year.
    };

    function localeFirstDayOfWeek () {
        return this._week.dow;
    }

    function localeFirstDayOfYear () {
        return this._week.doy;
    }

    // MOMENTS

    function getSetWeek (input) {
        var week = this.localeData().week(this);
        return input == null ? week : this.add((input - week) * 7, 'd');
    }

    function getSetISOWeek (input) {
        var week = weekOfYear(this, 1, 4).week;
        return input == null ? week : this.add((input - week) * 7, 'd');
    }

    // FORMATTING

    addFormatToken('d', 0, 'do', 'day');

    addFormatToken('dd', 0, 0, function (format) {
        return this.localeData().weekdaysMin(this, format);
    });

    addFormatToken('ddd', 0, 0, function (format) {
        return this.localeData().weekdaysShort(this, format);
    });

    addFormatToken('dddd', 0, 0, function (format) {
        return this.localeData().weekdays(this, format);
    });

    addFormatToken('e', 0, 0, 'weekday');
    addFormatToken('E', 0, 0, 'isoWeekday');

    // ALIASES

    addUnitAlias('day', 'd');
    addUnitAlias('weekday', 'e');
    addUnitAlias('isoWeekday', 'E');

    // PRIORITY
    addUnitPriority('day', 11);
    addUnitPriority('weekday', 11);
    addUnitPriority('isoWeekday', 11);

    // PARSING

    addRegexToken('d',    match1to2);
    addRegexToken('e',    match1to2);
    addRegexToken('E',    match1to2);
    addRegexToken('dd',   function (isStrict, locale) {
        return locale.weekdaysMinRegex(isStrict);
    });
    addRegexToken('ddd',   function (isStrict, locale) {
        return locale.weekdaysShortRegex(isStrict);
    });
    addRegexToken('dddd',   function (isStrict, locale) {
        return locale.weekdaysRegex(isStrict);
    });

    addWeekParseToken(['dd', 'ddd', 'dddd'], function (input, week, config, token) {
        var weekday = config._locale.weekdaysParse(input, token, config._strict);
        // if we didn't get a weekday name, mark the date as invalid
        if (weekday != null) {
            week.d = weekday;
        } else {
            getParsingFlags(config).invalidWeekday = input;
        }
    });

    addWeekParseToken(['d', 'e', 'E'], function (input, week, config, token) {
        week[token] = toInt(input);
    });

    // HELPERS

    function parseWeekday(input, locale) {
        if (typeof input !== 'string') {
            return input;
        }

        if (!isNaN(input)) {
            return parseInt(input, 10);
        }

        input = locale.weekdaysParse(input);
        if (typeof input === 'number') {
            return input;
        }

        return null;
    }

    function parseIsoWeekday(input, locale) {
        if (typeof input === 'string') {
            return locale.weekdaysParse(input) % 7 || 7;
        }
        return isNaN(input) ? null : input;
    }

    // LOCALES

    var defaultLocaleWeekdays = 'Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday'.split('_');
    function localeWeekdays (m, format) {
        return isArray(this._weekdays) ? this._weekdays[m.day()] :
            this._weekdays[this._weekdays.isFormat.test(format) ? 'format' : 'standalone'][m.day()];
    }

    var defaultLocaleWeekdaysShort = 'Sun_Mon_Tue_Wed_Thu_Fri_Sat'.split('_');
    function localeWeekdaysShort (m) {
        return this._weekdaysShort[m.day()];
    }

    var defaultLocaleWeekdaysMin = 'Su_Mo_Tu_We_Th_Fr_Sa'.split('_');
    function localeWeekdaysMin (m) {
        return this._weekdaysMin[m.day()];
    }

    function day_of_week__handleStrictParse(weekdayName, format, strict) {
        var i, ii, mom, llc = weekdayName.toLocaleLowerCase();
        if (!this._weekdaysParse) {
            this._weekdaysParse = [];
            this._shortWeekdaysParse = [];
            this._minWeekdaysParse = [];

            for (i = 0; i < 7; ++i) {
                mom = create_utc__createUTC([2000, 1]).day(i);
                this._minWeekdaysParse[i] = this.weekdaysMin(mom, '').toLocaleLowerCase();
                this._shortWeekdaysParse[i] = this.weekdaysShort(mom, '').toLocaleLowerCase();
                this._weekdaysParse[i] = this.weekdays(mom, '').toLocaleLowerCase();
            }
        }

        if (strict) {
            if (format === 'dddd') {
                ii = indexOf.call(this._weekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else if (format === 'ddd') {
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._minWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            }
        } else {
            if (format === 'dddd') {
                ii = indexOf.call(this._weekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._minWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else if (format === 'ddd') {
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._weekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._minWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            } else {
                ii = indexOf.call(this._minWeekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._weekdaysParse, llc);
                if (ii !== -1) {
                    return ii;
                }
                ii = indexOf.call(this._shortWeekdaysParse, llc);
                return ii !== -1 ? ii : null;
            }
        }
    }

    function localeWeekdaysParse (weekdayName, format, strict) {
        var i, mom, regex;

        if (this._weekdaysParseExact) {
            return day_of_week__handleStrictParse.call(this, weekdayName, format, strict);
        }

        if (!this._weekdaysParse) {
            this._weekdaysParse = [];
            this._minWeekdaysParse = [];
            this._shortWeekdaysParse = [];
            this._fullWeekdaysParse = [];
        }

        for (i = 0; i < 7; i++) {
            // make the regex if we don't have it already

            mom = create_utc__createUTC([2000, 1]).day(i);
            if (strict && !this._fullWeekdaysParse[i]) {
                this._fullWeekdaysParse[i] = new RegExp('^' + this.weekdays(mom, '').replace('.', '\.?') + '$', 'i');
                this._shortWeekdaysParse[i] = new RegExp('^' + this.weekdaysShort(mom, '').replace('.', '\.?') + '$', 'i');
                this._minWeekdaysParse[i] = new RegExp('^' + this.weekdaysMin(mom, '').replace('.', '\.?') + '$', 'i');
            }
            if (!this._weekdaysParse[i]) {
                regex = '^' + this.weekdays(mom, '') + '|^' + this.weekdaysShort(mom, '') + '|^' + this.weekdaysMin(mom, '');
                this._weekdaysParse[i] = new RegExp(regex.replace('.', ''), 'i');
            }
            // test the regex
            if (strict && format === 'dddd' && this._fullWeekdaysParse[i].test(weekdayName)) {
                return i;
            } else if (strict && format === 'ddd' && this._shortWeekdaysParse[i].test(weekdayName)) {
                return i;
            } else if (strict && format === 'dd' && this._minWeekdaysParse[i].test(weekdayName)) {
                return i;
            } else if (!strict && this._weekdaysParse[i].test(weekdayName)) {
                return i;
            }
        }
    }

    // MOMENTS

    function getSetDayOfWeek (input) {
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }
        var day = this._isUTC ? this._d.getUTCDay() : this._d.getDay();
        if (input != null) {
            input = parseWeekday(input, this.localeData());
            return this.add(input - day, 'd');
        } else {
            return day;
        }
    }

    function getSetLocaleDayOfWeek (input) {
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }
        var weekday = (this.day() + 7 - this.localeData()._week.dow) % 7;
        return input == null ? weekday : this.add(input - weekday, 'd');
    }

    function getSetISODayOfWeek (input) {
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }

        // behaves the same as moment#day except
        // as a getter, returns 7 instead of 0 (1-7 range instead of 0-6)
        // as a setter, sunday should belong to the previous week.

        if (input != null) {
            var weekday = parseIsoWeekday(input, this.localeData());
            return this.day(this.day() % 7 ? weekday : weekday - 7);
        } else {
            return this.day() || 7;
        }
    }

    var defaultWeekdaysRegex = matchWord;
    function weekdaysRegex (isStrict) {
        if (this._weekdaysParseExact) {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                computeWeekdaysParse.call(this);
            }
            if (isStrict) {
                return this._weekdaysStrictRegex;
            } else {
                return this._weekdaysRegex;
            }
        } else {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                this._weekdaysRegex = defaultWeekdaysRegex;
            }
            return this._weekdaysStrictRegex && isStrict ?
                this._weekdaysStrictRegex : this._weekdaysRegex;
        }
    }

    var defaultWeekdaysShortRegex = matchWord;
    function weekdaysShortRegex (isStrict) {
        if (this._weekdaysParseExact) {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                computeWeekdaysParse.call(this);
            }
            if (isStrict) {
                return this._weekdaysShortStrictRegex;
            } else {
                return this._weekdaysShortRegex;
            }
        } else {
            if (!hasOwnProp(this, '_weekdaysShortRegex')) {
                this._weekdaysShortRegex = defaultWeekdaysShortRegex;
            }
            return this._weekdaysShortStrictRegex && isStrict ?
                this._weekdaysShortStrictRegex : this._weekdaysShortRegex;
        }
    }

    var defaultWeekdaysMinRegex = matchWord;
    function weekdaysMinRegex (isStrict) {
        if (this._weekdaysParseExact) {
            if (!hasOwnProp(this, '_weekdaysRegex')) {
                computeWeekdaysParse.call(this);
            }
            if (isStrict) {
                return this._weekdaysMinStrictRegex;
            } else {
                return this._weekdaysMinRegex;
            }
        } else {
            if (!hasOwnProp(this, '_weekdaysMinRegex')) {
                this._weekdaysMinRegex = defaultWeekdaysMinRegex;
            }
            return this._weekdaysMinStrictRegex && isStrict ?
                this._weekdaysMinStrictRegex : this._weekdaysMinRegex;
        }
    }


    function computeWeekdaysParse () {
        function cmpLenRev(a, b) {
            return b.length - a.length;
        }

        var minPieces = [], shortPieces = [], longPieces = [], mixedPieces = [],
            i, mom, minp, shortp, longp;
        for (i = 0; i < 7; i++) {
            // make the regex if we don't have it already
            mom = create_utc__createUTC([2000, 1]).day(i);
            minp = this.weekdaysMin(mom, '');
            shortp = this.weekdaysShort(mom, '');
            longp = this.weekdays(mom, '');
            minPieces.push(minp);
            shortPieces.push(shortp);
            longPieces.push(longp);
            mixedPieces.push(minp);
            mixedPieces.push(shortp);
            mixedPieces.push(longp);
        }
        // Sorting makes sure if one weekday (or abbr) is a prefix of another it
        // will match the longer piece.
        minPieces.sort(cmpLenRev);
        shortPieces.sort(cmpLenRev);
        longPieces.sort(cmpLenRev);
        mixedPieces.sort(cmpLenRev);
        for (i = 0; i < 7; i++) {
            shortPieces[i] = regexEscape(shortPieces[i]);
            longPieces[i] = regexEscape(longPieces[i]);
            mixedPieces[i] = regexEscape(mixedPieces[i]);
        }

        this._weekdaysRegex = new RegExp('^(' + mixedPieces.join('|') + ')', 'i');
        this._weekdaysShortRegex = this._weekdaysRegex;
        this._weekdaysMinRegex = this._weekdaysRegex;

        this._weekdaysStrictRegex = new RegExp('^(' + longPieces.join('|') + ')', 'i');
        this._weekdaysShortStrictRegex = new RegExp('^(' + shortPieces.join('|') + ')', 'i');
        this._weekdaysMinStrictRegex = new RegExp('^(' + minPieces.join('|') + ')', 'i');
    }

    // FORMATTING

    function hFormat() {
        return this.hours() % 12 || 12;
    }

    function kFormat() {
        return this.hours() || 24;
    }

    addFormatToken('H', ['HH', 2], 0, 'hour');
    addFormatToken('h', ['hh', 2], 0, hFormat);
    addFormatToken('k', ['kk', 2], 0, kFormat);

    addFormatToken('hmm', 0, 0, function () {
        return '' + hFormat.apply(this) + zeroFill(this.minutes(), 2);
    });

    addFormatToken('hmmss', 0, 0, function () {
        return '' + hFormat.apply(this) + zeroFill(this.minutes(), 2) +
            zeroFill(this.seconds(), 2);
    });

    addFormatToken('Hmm', 0, 0, function () {
        return '' + this.hours() + zeroFill(this.minutes(), 2);
    });

    addFormatToken('Hmmss', 0, 0, function () {
        return '' + this.hours() + zeroFill(this.minutes(), 2) +
            zeroFill(this.seconds(), 2);
    });

    function meridiem (token, lowercase) {
        addFormatToken(token, 0, 0, function () {
            return this.localeData().meridiem(this.hours(), this.minutes(), lowercase);
        });
    }

    meridiem('a', true);
    meridiem('A', false);

    // ALIASES

    addUnitAlias('hour', 'h');

    // PRIORITY
    addUnitPriority('hour', 13);

    // PARSING

    function matchMeridiem (isStrict, locale) {
        return locale._meridiemParse;
    }

    addRegexToken('a',  matchMeridiem);
    addRegexToken('A',  matchMeridiem);
    addRegexToken('H',  match1to2);
    addRegexToken('h',  match1to2);
    addRegexToken('HH', match1to2, match2);
    addRegexToken('hh', match1to2, match2);

    addRegexToken('hmm', match3to4);
    addRegexToken('hmmss', match5to6);
    addRegexToken('Hmm', match3to4);
    addRegexToken('Hmmss', match5to6);

    addParseToken(['H', 'HH'], HOUR);
    addParseToken(['a', 'A'], function (input, array, config) {
        config._isPm = config._locale.isPM(input);
        config._meridiem = input;
    });
    addParseToken(['h', 'hh'], function (input, array, config) {
        array[HOUR] = toInt(input);
        getParsingFlags(config).bigHour = true;
    });
    addParseToken('hmm', function (input, array, config) {
        var pos = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos));
        array[MINUTE] = toInt(input.substr(pos));
        getParsingFlags(config).bigHour = true;
    });
    addParseToken('hmmss', function (input, array, config) {
        var pos1 = input.length - 4;
        var pos2 = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos1));
        array[MINUTE] = toInt(input.substr(pos1, 2));
        array[SECOND] = toInt(input.substr(pos2));
        getParsingFlags(config).bigHour = true;
    });
    addParseToken('Hmm', function (input, array, config) {
        var pos = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos));
        array[MINUTE] = toInt(input.substr(pos));
    });
    addParseToken('Hmmss', function (input, array, config) {
        var pos1 = input.length - 4;
        var pos2 = input.length - 2;
        array[HOUR] = toInt(input.substr(0, pos1));
        array[MINUTE] = toInt(input.substr(pos1, 2));
        array[SECOND] = toInt(input.substr(pos2));
    });

    // LOCALES

    function localeIsPM (input) {
        // IE8 Quirks Mode & IE7 Standards Mode do not allow accessing strings like arrays
        // Using charAt should be more compatible.
        return ((input + '').toLowerCase().charAt(0) === 'p');
    }

    var defaultLocaleMeridiemParse = /[ap]\.?m?\.?/i;
    function localeMeridiem (hours, minutes, isLower) {
        if (hours > 11) {
            return isLower ? 'pm' : 'PM';
        } else {
            return isLower ? 'am' : 'AM';
        }
    }


    // MOMENTS

    // Setting the hour should keep the time, because the user explicitly
    // specified which hour he wants. So trying to maintain the same hour (in
    // a new timezone) makes sense. Adding/subtracting hours does not follow
    // this rule.
    var getSetHour = makeGetSet('Hours', true);

    var baseConfig = {
        calendar: defaultCalendar,
        longDateFormat: defaultLongDateFormat,
        invalidDate: defaultInvalidDate,
        ordinal: defaultOrdinal,
        ordinalParse: defaultOrdinalParse,
        relativeTime: defaultRelativeTime,

        months: defaultLocaleMonths,
        monthsShort: defaultLocaleMonthsShort,

        week: defaultLocaleWeek,

        weekdays: defaultLocaleWeekdays,
        weekdaysMin: defaultLocaleWeekdaysMin,
        weekdaysShort: defaultLocaleWeekdaysShort,

        meridiemParse: defaultLocaleMeridiemParse
    };

    // internal storage for locale config files
    var locales = {};
    var globalLocale;

    function normalizeLocale(key) {
        return key ? key.toLowerCase().replace('_', '-') : key;
    }

    // pick the locale from the array
    // try ['en-au', 'en-gb'] as 'en-au', 'en-gb', 'en', as in move through the list trying each
    // substring from most specific to least, but move to the next array item if it's a more specific variant than the current root
    function chooseLocale(names) {
        var i = 0, j, next, locale, split;

        while (i < names.length) {
            split = normalizeLocale(names[i]).split('-');
            j = split.length;
            next = normalizeLocale(names[i + 1]);
            next = next ? next.split('-') : null;
            while (j > 0) {
                locale = loadLocale(split.slice(0, j).join('-'));
                if (locale) {
                    return locale;
                }
                if (next && next.length >= j && compareArrays(split, next, true) >= j - 1) {
                    //the next array item is better than a shallower substring of this one
                    break;
                }
                j--;
            }
            i++;
        }
        return null;
    }

    function loadLocale(name) {
        var oldLocale = null;
        // TODO: Find a better way to register and load all the locales in Node
        if (!locales[name] && (typeof module !== 'undefined') &&
                module && module.exports) {
            try {
                oldLocale = globalLocale._abbr;
                require('./locale/' + name);
                // because defineLocale currently also sets the global locale, we
                // want to undo that for lazy loaded locales
                locale_locales__getSetGlobalLocale(oldLocale);
            } catch (e) { }
        }
        return locales[name];
    }

    // This function will load locale and then set the global locale.  If
    // no arguments are passed in, it will simply return the current global
    // locale key.
    function locale_locales__getSetGlobalLocale (key, values) {
        var data;
        if (key) {
            if (isUndefined(values)) {
                data = locale_locales__getLocale(key);
            }
            else {
                data = defineLocale(key, values);
            }

            if (data) {
                // moment.duration._locale = moment._locale = data;
                globalLocale = data;
            }
        }

        return globalLocale._abbr;
    }

    function defineLocale (name, config) {
        if (config !== null) {
            var parentConfig = baseConfig;
            config.abbr = name;
            if (locales[name] != null) {
                deprecateSimple('defineLocaleOverride',
                        'use moment.updateLocale(localeName, config) to change ' +
                        'an existing locale. moment.defineLocale(localeName, ' +
                        'config) should only be used for creating a new locale ' +
                        'See http://momentjs.com/guides/#/warnings/define-locale/ for more info.');
                parentConfig = locales[name]._config;
            } else if (config.parentLocale != null) {
                if (locales[config.parentLocale] != null) {
                    parentConfig = locales[config.parentLocale]._config;
                } else {
                    // treat as if there is no base config
                    deprecateSimple('parentLocaleUndefined',
                            'specified parentLocale is not defined yet. See http://momentjs.com/guides/#/warnings/parent-locale/');
                }
            }
            locales[name] = new Locale(mergeConfigs(parentConfig, config));

            // backwards compat for now: also set the locale
            locale_locales__getSetGlobalLocale(name);

            return locales[name];
        } else {
            // useful for testing
            delete locales[name];
            return null;
        }
    }

    function updateLocale(name, config) {
        if (config != null) {
            var locale, parentConfig = baseConfig;
            // MERGE
            if (locales[name] != null) {
                parentConfig = locales[name]._config;
            }
            config = mergeConfigs(parentConfig, config);
            locale = new Locale(config);
            locale.parentLocale = locales[name];
            locales[name] = locale;

            // backwards compat for now: also set the locale
            locale_locales__getSetGlobalLocale(name);
        } else {
            // pass null for config to unupdate, useful for tests
            if (locales[name] != null) {
                if (locales[name].parentLocale != null) {
                    locales[name] = locales[name].parentLocale;
                } else if (locales[name] != null) {
                    delete locales[name];
                }
            }
        }
        return locales[name];
    }

    // returns locale data
    function locale_locales__getLocale (key) {
        var locale;

        if (key && key._locale && key._locale._abbr) {
            key = key._locale._abbr;
        }

        if (!key) {
            return globalLocale;
        }

        if (!isArray(key)) {
            //short-circuit everything else
            locale = loadLocale(key);
            if (locale) {
                return locale;
            }
            key = [key];
        }

        return chooseLocale(key);
    }

    function locale_locales__listLocales() {
        return keys(locales);
    }

    function checkOverflow (m) {
        var overflow;
        var a = m._a;

        if (a && getParsingFlags(m).overflow === -2) {
            overflow =
                a[MONTH]       < 0 || a[MONTH]       > 11  ? MONTH :
                a[DATE]        < 1 || a[DATE]        > daysInMonth(a[YEAR], a[MONTH]) ? DATE :
                a[HOUR]        < 0 || a[HOUR]        > 24 || (a[HOUR] === 24 && (a[MINUTE] !== 0 || a[SECOND] !== 0 || a[MILLISECOND] !== 0)) ? HOUR :
                a[MINUTE]      < 0 || a[MINUTE]      > 59  ? MINUTE :
                a[SECOND]      < 0 || a[SECOND]      > 59  ? SECOND :
                a[MILLISECOND] < 0 || a[MILLISECOND] > 999 ? MILLISECOND :
                -1;

            if (getParsingFlags(m)._overflowDayOfYear && (overflow < YEAR || overflow > DATE)) {
                overflow = DATE;
            }
            if (getParsingFlags(m)._overflowWeeks && overflow === -1) {
                overflow = WEEK;
            }
            if (getParsingFlags(m)._overflowWeekday && overflow === -1) {
                overflow = WEEKDAY;
            }

            getParsingFlags(m).overflow = overflow;
        }

        return m;
    }

    // iso 8601 regex
    // 0000-00-00 0000-W00 or 0000-W00-0 + T + 00 or 00:00 or 00:00:00 or 00:00:00.000 + +00:00 or +0000 or +00)
    var extendedIsoRegex = /^\s*((?:[+-]\d{6}|\d{4})-(?:\d\d-\d\d|W\d\d-\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?::\d\d(?::\d\d(?:[.,]\d+)?)?)?)([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?/;
    var basicIsoRegex = /^\s*((?:[+-]\d{6}|\d{4})(?:\d\d\d\d|W\d\d\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?:\d\d(?:\d\d(?:[.,]\d+)?)?)?)([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?/;

    var tzRegex = /Z|[+-]\d\d(?::?\d\d)?/;

    var isoDates = [
        ['YYYYYY-MM-DD', /[+-]\d{6}-\d\d-\d\d/],
        ['YYYY-MM-DD', /\d{4}-\d\d-\d\d/],
        ['GGGG-[W]WW-E', /\d{4}-W\d\d-\d/],
        ['GGGG-[W]WW', /\d{4}-W\d\d/, false],
        ['YYYY-DDD', /\d{4}-\d{3}/],
        ['YYYY-MM', /\d{4}-\d\d/, false],
        ['YYYYYYMMDD', /[+-]\d{10}/],
        ['YYYYMMDD', /\d{8}/],
        // YYYYMM is NOT allowed by the standard
        ['GGGG[W]WWE', /\d{4}W\d{3}/],
        ['GGGG[W]WW', /\d{4}W\d{2}/, false],
        ['YYYYDDD', /\d{7}/]
    ];

    // iso time formats and regexes
    var isoTimes = [
        ['HH:mm:ss.SSSS', /\d\d:\d\d:\d\d\.\d+/],
        ['HH:mm:ss,SSSS', /\d\d:\d\d:\d\d,\d+/],
        ['HH:mm:ss', /\d\d:\d\d:\d\d/],
        ['HH:mm', /\d\d:\d\d/],
        ['HHmmss.SSSS', /\d\d\d\d\d\d\.\d+/],
        ['HHmmss,SSSS', /\d\d\d\d\d\d,\d+/],
        ['HHmmss', /\d\d\d\d\d\d/],
        ['HHmm', /\d\d\d\d/],
        ['HH', /\d\d/]
    ];

    var aspNetJsonRegex = /^\/?Date\((\-?\d+)/i;

    // date from iso format
    function configFromISO(config) {
        var i, l,
            string = config._i,
            match = extendedIsoRegex.exec(string) || basicIsoRegex.exec(string),
            allowTime, dateFormat, timeFormat, tzFormat;

        if (match) {
            getParsingFlags(config).iso = true;

            for (i = 0, l = isoDates.length; i < l; i++) {
                if (isoDates[i][1].exec(match[1])) {
                    dateFormat = isoDates[i][0];
                    allowTime = isoDates[i][2] !== false;
                    break;
                }
            }
            if (dateFormat == null) {
                config._isValid = false;
                return;
            }
            if (match[3]) {
                for (i = 0, l = isoTimes.length; i < l; i++) {
                    if (isoTimes[i][1].exec(match[3])) {
                        // match[2] should be 'T' or space
                        timeFormat = (match[2] || ' ') + isoTimes[i][0];
                        break;
                    }
                }
                if (timeFormat == null) {
                    config._isValid = false;
                    return;
                }
            }
            if (!allowTime && timeFormat != null) {
                config._isValid = false;
                return;
            }
            if (match[4]) {
                if (tzRegex.exec(match[4])) {
                    tzFormat = 'Z';
                } else {
                    config._isValid = false;
                    return;
                }
            }
            config._f = dateFormat + (timeFormat || '') + (tzFormat || '');
            configFromStringAndFormat(config);
        } else {
            config._isValid = false;
        }
    }

    // date from iso format or fallback
    function configFromString(config) {
        var matched = aspNetJsonRegex.exec(config._i);

        if (matched !== null) {
            config._d = new Date(+matched[1]);
            return;
        }

        configFromISO(config);
        if (config._isValid === false) {
            delete config._isValid;
            utils_hooks__hooks.createFromInputFallback(config);
        }
    }

    utils_hooks__hooks.createFromInputFallback = deprecate(
        'moment construction falls back to js Date. This is ' +
        'discouraged and will be removed in upcoming major ' +
        'release. Please refer to ' +
        'http://momentjs.com/guides/#/warnings/js-date/ for more info.',
        function (config) {
            config._d = new Date(config._i + (config._useUTC ? ' UTC' : ''));
        }
    );

    // Pick the first defined of two or three arguments.
    function defaults(a, b, c) {
        if (a != null) {
            return a;
        }
        if (b != null) {
            return b;
        }
        return c;
    }

    function currentDateArray(config) {
        // hooks is actually the exported moment object
        var nowValue = new Date(utils_hooks__hooks.now());
        if (config._useUTC) {
            return [nowValue.getUTCFullYear(), nowValue.getUTCMonth(), nowValue.getUTCDate()];
        }
        return [nowValue.getFullYear(), nowValue.getMonth(), nowValue.getDate()];
    }

    // convert an array to a date.
    // the array should mirror the parameters below
    // note: all values past the year are optional and will default to the lowest possible value.
    // [year, month, day , hour, minute, second, millisecond]
    function configFromArray (config) {
        var i, date, input = [], currentDate, yearToUse;

        if (config._d) {
            return;
        }

        currentDate = currentDateArray(config);

        //compute day of the year from weeks and weekdays
        if (config._w && config._a[DATE] == null && config._a[MONTH] == null) {
            dayOfYearFromWeekInfo(config);
        }

        //if the day of the year is set, figure out what it is
        if (config._dayOfYear) {
            yearToUse = defaults(config._a[YEAR], currentDate[YEAR]);

            if (config._dayOfYear > daysInYear(yearToUse)) {
                getParsingFlags(config)._overflowDayOfYear = true;
            }

            date = createUTCDate(yearToUse, 0, config._dayOfYear);
            config._a[MONTH] = date.getUTCMonth();
            config._a[DATE] = date.getUTCDate();
        }

        // Default to current date.
        // * if no year, month, day of month are given, default to today
        // * if day of month is given, default month and year
        // * if month is given, default only year
        // * if year is given, don't default anything
        for (i = 0; i < 3 && config._a[i] == null; ++i) {
            config._a[i] = input[i] = currentDate[i];
        }

        // Zero out whatever was not defaulted, including time
        for (; i < 7; i++) {
            config._a[i] = input[i] = (config._a[i] == null) ? (i === 2 ? 1 : 0) : config._a[i];
        }

        // Check for 24:00:00.000
        if (config._a[HOUR] === 24 &&
                config._a[MINUTE] === 0 &&
                config._a[SECOND] === 0 &&
                config._a[MILLISECOND] === 0) {
            config._nextDay = true;
            config._a[HOUR] = 0;
        }

        config._d = (config._useUTC ? createUTCDate : createDate).apply(null, input);
        // Apply timezone offset from input. The actual utcOffset can be changed
        // with parseZone.
        if (config._tzm != null) {
            config._d.setUTCMinutes(config._d.getUTCMinutes() - config._tzm);
        }

        if (config._nextDay) {
            config._a[HOUR] = 24;
        }
    }

    function dayOfYearFromWeekInfo(config) {
        var w, weekYear, week, weekday, dow, doy, temp, weekdayOverflow;

        w = config._w;
        if (w.GG != null || w.W != null || w.E != null) {
            dow = 1;
            doy = 4;

            // TODO: We need to take the current isoWeekYear, but that depends on
            // how we interpret now (local, utc, fixed offset). So create
            // a now version of current config (take local/utc/offset flags, and
            // create now).
            weekYear = defaults(w.GG, config._a[YEAR], weekOfYear(local__createLocal(), 1, 4).year);
            week = defaults(w.W, 1);
            weekday = defaults(w.E, 1);
            if (weekday < 1 || weekday > 7) {
                weekdayOverflow = true;
            }
        } else {
            dow = config._locale._week.dow;
            doy = config._locale._week.doy;

            weekYear = defaults(w.gg, config._a[YEAR], weekOfYear(local__createLocal(), dow, doy).year);
            week = defaults(w.w, 1);

            if (w.d != null) {
                // weekday -- low day numbers are considered next week
                weekday = w.d;
                if (weekday < 0 || weekday > 6) {
                    weekdayOverflow = true;
                }
            } else if (w.e != null) {
                // local weekday -- counting starts from begining of week
                weekday = w.e + dow;
                if (w.e < 0 || w.e > 6) {
                    weekdayOverflow = true;
                }
            } else {
                // default to begining of week
                weekday = dow;
            }
        }
        if (week < 1 || week > weeksInYear(weekYear, dow, doy)) {
            getParsingFlags(config)._overflowWeeks = true;
        } else if (weekdayOverflow != null) {
            getParsingFlags(config)._overflowWeekday = true;
        } else {
            temp = dayOfYearFromWeeks(weekYear, week, weekday, dow, doy);
            config._a[YEAR] = temp.year;
            config._dayOfYear = temp.dayOfYear;
        }
    }

    // constant that refers to the ISO standard
    utils_hooks__hooks.ISO_8601 = function () {};

    // date from string and format string
    function configFromStringAndFormat(config) {
        // TODO: Move this to another part of the creation flow to prevent circular deps
        if (config._f === utils_hooks__hooks.ISO_8601) {
            configFromISO(config);
            return;
        }

        config._a = [];
        getParsingFlags(config).empty = true;

        // This array is used to make a Date, either with `new Date` or `Date.UTC`
        var string = '' + config._i,
            i, parsedInput, tokens, token, skipped,
            stringLength = string.length,
            totalParsedInputLength = 0;

        tokens = expandFormat(config._f, config._locale).match(formattingTokens) || [];

        for (i = 0; i < tokens.length; i++) {
            token = tokens[i];
            parsedInput = (string.match(getParseRegexForToken(token, config)) || [])[0];
            // console.log('token', token, 'parsedInput', parsedInput,
            //         'regex', getParseRegexForToken(token, config));
            if (parsedInput) {
                skipped = string.substr(0, string.indexOf(parsedInput));
                if (skipped.length > 0) {
                    getParsingFlags(config).unusedInput.push(skipped);
                }
                string = string.slice(string.indexOf(parsedInput) + parsedInput.length);
                totalParsedInputLength += parsedInput.length;
            }
            // don't parse if it's not a known token
            if (formatTokenFunctions[token]) {
                if (parsedInput) {
                    getParsingFlags(config).empty = false;
                }
                else {
                    getParsingFlags(config).unusedTokens.push(token);
                }
                addTimeToArrayFromToken(token, parsedInput, config);
            }
            else if (config._strict && !parsedInput) {
                getParsingFlags(config).unusedTokens.push(token);
            }
        }

        // add remaining unparsed input length to the string
        getParsingFlags(config).charsLeftOver = stringLength - totalParsedInputLength;
        if (string.length > 0) {
            getParsingFlags(config).unusedInput.push(string);
        }

        // clear _12h flag if hour is <= 12
        if (config._a[HOUR] <= 12 &&
            getParsingFlags(config).bigHour === true &&
            config._a[HOUR] > 0) {
            getParsingFlags(config).bigHour = undefined;
        }

        getParsingFlags(config).parsedDateParts = config._a.slice(0);
        getParsingFlags(config).meridiem = config._meridiem;
        // handle meridiem
        config._a[HOUR] = meridiemFixWrap(config._locale, config._a[HOUR], config._meridiem);

        configFromArray(config);
        checkOverflow(config);
    }


    function meridiemFixWrap (locale, hour, meridiem) {
        var isPm;

        if (meridiem == null) {
            // nothing to do
            return hour;
        }
        if (locale.meridiemHour != null) {
            return locale.meridiemHour(hour, meridiem);
        } else if (locale.isPM != null) {
            // Fallback
            isPm = locale.isPM(meridiem);
            if (isPm && hour < 12) {
                hour += 12;
            }
            if (!isPm && hour === 12) {
                hour = 0;
            }
            return hour;
        } else {
            // this is not supposed to happen
            return hour;
        }
    }

    // date from string and array of format strings
    function configFromStringAndArray(config) {
        var tempConfig,
            bestMoment,

            scoreToBeat,
            i,
            currentScore;

        if (config._f.length === 0) {
            getParsingFlags(config).invalidFormat = true;
            config._d = new Date(NaN);
            return;
        }

        for (i = 0; i < config._f.length; i++) {
            currentScore = 0;
            tempConfig = copyConfig({}, config);
            if (config._useUTC != null) {
                tempConfig._useUTC = config._useUTC;
            }
            tempConfig._f = config._f[i];
            configFromStringAndFormat(tempConfig);

            if (!valid__isValid(tempConfig)) {
                continue;
            }

            // if there is any input that was not parsed add a penalty for that format
            currentScore += getParsingFlags(tempConfig).charsLeftOver;

            //or tokens
            currentScore += getParsingFlags(tempConfig).unusedTokens.length * 10;

            getParsingFlags(tempConfig).score = currentScore;

            if (scoreToBeat == null || currentScore < scoreToBeat) {
                scoreToBeat = currentScore;
                bestMoment = tempConfig;
            }
        }

        extend(config, bestMoment || tempConfig);
    }

    function configFromObject(config) {
        if (config._d) {
            return;
        }

        var i = normalizeObjectUnits(config._i);
        config._a = map([i.year, i.month, i.day || i.date, i.hour, i.minute, i.second, i.millisecond], function (obj) {
            return obj && parseInt(obj, 10);
        });

        configFromArray(config);
    }

    function createFromConfig (config) {
        var res = new Moment(checkOverflow(prepareConfig(config)));
        if (res._nextDay) {
            // Adding is smart enough around DST
            res.add(1, 'd');
            res._nextDay = undefined;
        }

        return res;
    }

    function prepareConfig (config) {
        var input = config._i,
            format = config._f;

        config._locale = config._locale || locale_locales__getLocale(config._l);

        if (input === null || (format === undefined && input === '')) {
            return valid__createInvalid({nullInput: true});
        }

        if (typeof input === 'string') {
            config._i = input = config._locale.preparse(input);
        }

        if (isMoment(input)) {
            return new Moment(checkOverflow(input));
        } else if (isArray(format)) {
            configFromStringAndArray(config);
        } else if (isDate(input)) {
            config._d = input;
        } else if (format) {
            configFromStringAndFormat(config);
        }  else {
            configFromInput(config);
        }

        if (!valid__isValid(config)) {
            config._d = null;
        }

        return config;
    }

    function configFromInput(config) {
        var input = config._i;
        if (input === undefined) {
            config._d = new Date(utils_hooks__hooks.now());
        } else if (isDate(input)) {
            config._d = new Date(input.valueOf());
        } else if (typeof input === 'string') {
            configFromString(config);
        } else if (isArray(input)) {
            config._a = map(input.slice(0), function (obj) {
                return parseInt(obj, 10);
            });
            configFromArray(config);
        } else if (typeof(input) === 'object') {
            configFromObject(config);
        } else if (typeof(input) === 'number') {
            // from milliseconds
            config._d = new Date(input);
        } else {
            utils_hooks__hooks.createFromInputFallback(config);
        }
    }

    function createLocalOrUTC (input, format, locale, strict, isUTC) {
        var c = {};

        if (typeof(locale) === 'boolean') {
            strict = locale;
            locale = undefined;
        }

        if ((isObject(input) && isObjectEmpty(input)) ||
                (isArray(input) && input.length === 0)) {
            input = undefined;
        }
        // object construction must be done this way.
        // https://github.com/moment/moment/issues/1423
        c._isAMomentObject = true;
        c._useUTC = c._isUTC = isUTC;
        c._l = locale;
        c._i = input;
        c._f = format;
        c._strict = strict;

        return createFromConfig(c);
    }

    function local__createLocal (input, format, locale, strict) {
        return createLocalOrUTC(input, format, locale, strict, false);
    }

    var prototypeMin = deprecate(
        'moment().min is deprecated, use moment.max instead. http://momentjs.com/guides/#/warnings/min-max/',
        function () {
            var other = local__createLocal.apply(null, arguments);
            if (this.isValid() && other.isValid()) {
                return other < this ? this : other;
            } else {
                return valid__createInvalid();
            }
        }
    );

    var prototypeMax = deprecate(
        'moment().max is deprecated, use moment.min instead. http://momentjs.com/guides/#/warnings/min-max/',
        function () {
            var other = local__createLocal.apply(null, arguments);
            if (this.isValid() && other.isValid()) {
                return other > this ? this : other;
            } else {
                return valid__createInvalid();
            }
        }
    );

    // Pick a moment m from moments so that m[fn](other) is true for all
    // other. This relies on the function fn to be transitive.
    //
    // moments should either be an array of moment objects or an array, whose
    // first element is an array of moment objects.
    function pickBy(fn, moments) {
        var res, i;
        if (moments.length === 1 && isArray(moments[0])) {
            moments = moments[0];
        }
        if (!moments.length) {
            return local__createLocal();
        }
        res = moments[0];
        for (i = 1; i < moments.length; ++i) {
            if (!moments[i].isValid() || moments[i][fn](res)) {
                res = moments[i];
            }
        }
        return res;
    }

    // TODO: Use [].sort instead?
    function min () {
        var args = [].slice.call(arguments, 0);

        return pickBy('isBefore', args);
    }

    function max () {
        var args = [].slice.call(arguments, 0);

        return pickBy('isAfter', args);
    }

    var now = function () {
        return Date.now ? Date.now() : +(new Date());
    };

    function Duration (duration) {
        var normalizedInput = normalizeObjectUnits(duration),
            years = normalizedInput.year || 0,
            quarters = normalizedInput.quarter || 0,
            months = normalizedInput.month || 0,
            weeks = normalizedInput.week || 0,
            days = normalizedInput.day || 0,
            hours = normalizedInput.hour || 0,
            minutes = normalizedInput.minute || 0,
            seconds = normalizedInput.second || 0,
            milliseconds = normalizedInput.millisecond || 0;

        // representation for dateAddRemove
        this._milliseconds = +milliseconds +
            seconds * 1e3 + // 1000
            minutes * 6e4 + // 1000 * 60
            hours * 1000 * 60 * 60; //using 1000 * 60 * 60 instead of 36e5 to avoid floating point rounding errors https://github.com/moment/moment/issues/2978
        // Because of dateAddRemove treats 24 hours as different from a
        // day when working around DST, we need to store them separately
        this._days = +days +
            weeks * 7;
        // It is impossible translate months into days without knowing
        // which months you are are talking about, so we have to store
        // it separately.
        this._months = +months +
            quarters * 3 +
            years * 12;

        this._data = {};

        this._locale = locale_locales__getLocale();

        this._bubble();
    }

    function isDuration (obj) {
        return obj instanceof Duration;
    }

    // FORMATTING

    function offset (token, separator) {
        addFormatToken(token, 0, 0, function () {
            var offset = this.utcOffset();
            var sign = '+';
            if (offset < 0) {
                offset = -offset;
                sign = '-';
            }
            return sign + zeroFill(~~(offset / 60), 2) + separator + zeroFill(~~(offset) % 60, 2);
        });
    }

    offset('Z', ':');
    offset('ZZ', '');

    // PARSING

    addRegexToken('Z',  matchShortOffset);
    addRegexToken('ZZ', matchShortOffset);
    addParseToken(['Z', 'ZZ'], function (input, array, config) {
        config._useUTC = true;
        config._tzm = offsetFromString(matchShortOffset, input);
    });

    // HELPERS

    // timezone chunker
    // '+10:00' > ['10',  '00']
    // '-1530'  > ['-15', '30']
    var chunkOffset = /([\+\-]|\d\d)/gi;

    function offsetFromString(matcher, string) {
        var matches = ((string || '').match(matcher) || []);
        var chunk   = matches[matches.length - 1] || [];
        var parts   = (chunk + '').match(chunkOffset) || ['-', 0, 0];
        var minutes = +(parts[1] * 60) + toInt(parts[2]);

        return parts[0] === '+' ? minutes : -minutes;
    }

    // Return a moment from input, that is local/utc/zone equivalent to model.
    function cloneWithOffset(input, model) {
        var res, diff;
        if (model._isUTC) {
            res = model.clone();
            diff = (isMoment(input) || isDate(input) ? input.valueOf() : local__createLocal(input).valueOf()) - res.valueOf();
            // Use low-level api, because this fn is low-level api.
            res._d.setTime(res._d.valueOf() + diff);
            utils_hooks__hooks.updateOffset(res, false);
            return res;
        } else {
            return local__createLocal(input).local();
        }
    }

    function getDateOffset (m) {
        // On Firefox.24 Date#getTimezoneOffset returns a floating point.
        // https://github.com/moment/moment/pull/1871
        return -Math.round(m._d.getTimezoneOffset() / 15) * 15;
    }

    // HOOKS

    // This function will be called whenever a moment is mutated.
    // It is intended to keep the offset in sync with the timezone.
    utils_hooks__hooks.updateOffset = function () {};

    // MOMENTS

    // keepLocalTime = true means only change the timezone, without
    // affecting the local hour. So 5:31:26 +0300 --[utcOffset(2, true)]-->
    // 5:31:26 +0200 It is possible that 5:31:26 doesn't exist with offset
    // +0200, so we adjust the time as needed, to be valid.
    //
    // Keeping the time actually adds/subtracts (one hour)
    // from the actual represented time. That is why we call updateOffset
    // a second time. In case it wants us to change the offset again
    // _changeInProgress == true case, then we have to adjust, because
    // there is no such time in the given timezone.
    function getSetOffset (input, keepLocalTime) {
        var offset = this._offset || 0,
            localAdjust;
        if (!this.isValid()) {
            return input != null ? this : NaN;
        }
        if (input != null) {
            if (typeof input === 'string') {
                input = offsetFromString(matchShortOffset, input);
            } else if (Math.abs(input) < 16) {
                input = input * 60;
            }
            if (!this._isUTC && keepLocalTime) {
                localAdjust = getDateOffset(this);
            }
            this._offset = input;
            this._isUTC = true;
            if (localAdjust != null) {
                this.add(localAdjust, 'm');
            }
            if (offset !== input) {
                if (!keepLocalTime || this._changeInProgress) {
                    add_subtract__addSubtract(this, create__createDuration(input - offset, 'm'), 1, false);
                } else if (!this._changeInProgress) {
                    this._changeInProgress = true;
                    utils_hooks__hooks.updateOffset(this, true);
                    this._changeInProgress = null;
                }
            }
            return this;
        } else {
            return this._isUTC ? offset : getDateOffset(this);
        }
    }

    function getSetZone (input, keepLocalTime) {
        if (input != null) {
            if (typeof input !== 'string') {
                input = -input;
            }

            this.utcOffset(input, keepLocalTime);

            return this;
        } else {
            return -this.utcOffset();
        }
    }

    function setOffsetToUTC (keepLocalTime) {
        return this.utcOffset(0, keepLocalTime);
    }

    function setOffsetToLocal (keepLocalTime) {
        if (this._isUTC) {
            this.utcOffset(0, keepLocalTime);
            this._isUTC = false;

            if (keepLocalTime) {
                this.subtract(getDateOffset(this), 'm');
            }
        }
        return this;
    }

    function setOffsetToParsedOffset () {
        if (this._tzm) {
            this.utcOffset(this._tzm);
        } else if (typeof this._i === 'string') {
            this.utcOffset(offsetFromString(matchOffset, this._i));
        }
        return this;
    }

    function hasAlignedHourOffset (input) {
        if (!this.isValid()) {
            return false;
        }
        input = input ? local__createLocal(input).utcOffset() : 0;

        return (this.utcOffset() - input) % 60 === 0;
    }

    function isDaylightSavingTime () {
        return (
            this.utcOffset() > this.clone().month(0).utcOffset() ||
            this.utcOffset() > this.clone().month(5).utcOffset()
        );
    }

    function isDaylightSavingTimeShifted () {
        if (!isUndefined(this._isDSTShifted)) {
            return this._isDSTShifted;
        }

        var c = {};

        copyConfig(c, this);
        c = prepareConfig(c);

        if (c._a) {
            var other = c._isUTC ? create_utc__createUTC(c._a) : local__createLocal(c._a);
            this._isDSTShifted = this.isValid() &&
                compareArrays(c._a, other.toArray()) > 0;
        } else {
            this._isDSTShifted = false;
        }

        return this._isDSTShifted;
    }

    function isLocal () {
        return this.isValid() ? !this._isUTC : false;
    }

    function isUtcOffset () {
        return this.isValid() ? this._isUTC : false;
    }

    function isUtc () {
        return this.isValid() ? this._isUTC && this._offset === 0 : false;
    }

    // ASP.NET json date format regex
    var aspNetRegex = /^(\-)?(?:(\d*)[. ])?(\d+)\:(\d+)(?:\:(\d+)\.?(\d{3})?\d*)?$/;

    // from http://docs.closure-library.googlecode.com/git/closure_goog_date_date.js.source.html
    // somewhat more in line with 4.4.3.2 2004 spec, but allows decimal anywhere
    // and further modified to allow for strings containing both week and day
    var isoRegex = /^(-)?P(?:(-?[0-9,.]*)Y)?(?:(-?[0-9,.]*)M)?(?:(-?[0-9,.]*)W)?(?:(-?[0-9,.]*)D)?(?:T(?:(-?[0-9,.]*)H)?(?:(-?[0-9,.]*)M)?(?:(-?[0-9,.]*)S)?)?$/;

    function create__createDuration (input, key) {
        var duration = input,
            // matching against regexp is expensive, do it on demand
            match = null,
            sign,
            ret,
            diffRes;

        if (isDuration(input)) {
            duration = {
                ms : input._milliseconds,
                d  : input._days,
                M  : input._months
            };
        } else if (typeof input === 'number') {
            duration = {};
            if (key) {
                duration[key] = input;
            } else {
                duration.milliseconds = input;
            }
        } else if (!!(match = aspNetRegex.exec(input))) {
            sign = (match[1] === '-') ? -1 : 1;
            duration = {
                y  : 0,
                d  : toInt(match[DATE])        * sign,
                h  : toInt(match[HOUR])        * sign,
                m  : toInt(match[MINUTE])      * sign,
                s  : toInt(match[SECOND])      * sign,
                ms : toInt(match[MILLISECOND]) * sign
            };
        } else if (!!(match = isoRegex.exec(input))) {
            sign = (match[1] === '-') ? -1 : 1;
            duration = {
                y : parseIso(match[2], sign),
                M : parseIso(match[3], sign),
                w : parseIso(match[4], sign),
                d : parseIso(match[5], sign),
                h : parseIso(match[6], sign),
                m : parseIso(match[7], sign),
                s : parseIso(match[8], sign)
            };
        } else if (duration == null) {// checks for null or undefined
            duration = {};
        } else if (typeof duration === 'object' && ('from' in duration || 'to' in duration)) {
            diffRes = momentsDifference(local__createLocal(duration.from), local__createLocal(duration.to));

            duration = {};
            duration.ms = diffRes.milliseconds;
            duration.M = diffRes.months;
        }

        ret = new Duration(duration);

        if (isDuration(input) && hasOwnProp(input, '_locale')) {
            ret._locale = input._locale;
        }

        return ret;
    }

    create__createDuration.fn = Duration.prototype;

    function parseIso (inp, sign) {
        // We'd normally use ~~inp for this, but unfortunately it also
        // converts floats to ints.
        // inp may be undefined, so careful calling replace on it.
        var res = inp && parseFloat(inp.replace(',', '.'));
        // apply sign while we're at it
        return (isNaN(res) ? 0 : res) * sign;
    }

    function positiveMomentsDifference(base, other) {
        var res = {milliseconds: 0, months: 0};

        res.months = other.month() - base.month() +
            (other.year() - base.year()) * 12;
        if (base.clone().add(res.months, 'M').isAfter(other)) {
            --res.months;
        }

        res.milliseconds = +other - +(base.clone().add(res.months, 'M'));

        return res;
    }

    function momentsDifference(base, other) {
        var res;
        if (!(base.isValid() && other.isValid())) {
            return {milliseconds: 0, months: 0};
        }

        other = cloneWithOffset(other, base);
        if (base.isBefore(other)) {
            res = positiveMomentsDifference(base, other);
        } else {
            res = positiveMomentsDifference(other, base);
            res.milliseconds = -res.milliseconds;
            res.months = -res.months;
        }

        return res;
    }

    function absRound (number) {
        if (number < 0) {
            return Math.round(-1 * number) * -1;
        } else {
            return Math.round(number);
        }
    }

    // TODO: remove 'name' arg after deprecation is removed
    function createAdder(direction, name) {
        return function (val, period) {
            var dur, tmp;
            //invert the arguments, but complain about it
            if (period !== null && !isNaN(+period)) {
                deprecateSimple(name, 'moment().' + name  + '(period, number) is deprecated. Please use moment().' + name + '(number, period). ' +
                'See http://momentjs.com/guides/#/warnings/add-inverted-param/ for more info.');
                tmp = val; val = period; period = tmp;
            }

            val = typeof val === 'string' ? +val : val;
            dur = create__createDuration(val, period);
            add_subtract__addSubtract(this, dur, direction);
            return this;
        };
    }

    function add_subtract__addSubtract (mom, duration, isAdding, updateOffset) {
        var milliseconds = duration._milliseconds,
            days = absRound(duration._days),
            months = absRound(duration._months);

        if (!mom.isValid()) {
            // No op
            return;
        }

        updateOffset = updateOffset == null ? true : updateOffset;

        if (milliseconds) {
            mom._d.setTime(mom._d.valueOf() + milliseconds * isAdding);
        }
        if (days) {
            get_set__set(mom, 'Date', get_set__get(mom, 'Date') + days * isAdding);
        }
        if (months) {
            setMonth(mom, get_set__get(mom, 'Month') + months * isAdding);
        }
        if (updateOffset) {
            utils_hooks__hooks.updateOffset(mom, days || months);
        }
    }

    var add_subtract__add      = createAdder(1, 'add');
    var add_subtract__subtract = createAdder(-1, 'subtract');

    function getCalendarFormat(myMoment, now) {
        var diff = myMoment.diff(now, 'days', true);
        return diff < -6 ? 'sameElse' :
                diff < -1 ? 'lastWeek' :
                diff < 0 ? 'lastDay' :
                diff < 1 ? 'sameDay' :
                diff < 2 ? 'nextDay' :
                diff < 7 ? 'nextWeek' : 'sameElse';
    }

    function moment_calendar__calendar (time, formats) {
        // We want to compare the start of today, vs this.
        // Getting start-of-today depends on whether we're local/utc/offset or not.
        var now = time || local__createLocal(),
            sod = cloneWithOffset(now, this).startOf('day'),
            format = utils_hooks__hooks.calendarFormat(this, sod) || 'sameElse';

        var output = formats && (isFunction(formats[format]) ? formats[format].call(this, now) : formats[format]);

        return this.format(output || this.localeData().calendar(format, this, local__createLocal(now)));
    }

    function clone () {
        return new Moment(this);
    }

    function isAfter (input, units) {
        var localInput = isMoment(input) ? input : local__createLocal(input);
        if (!(this.isValid() && localInput.isValid())) {
            return false;
        }
        units = normalizeUnits(!isUndefined(units) ? units : 'millisecond');
        if (units === 'millisecond') {
            return this.valueOf() > localInput.valueOf();
        } else {
            return localInput.valueOf() < this.clone().startOf(units).valueOf();
        }
    }

    function isBefore (input, units) {
        var localInput = isMoment(input) ? input : local__createLocal(input);
        if (!(this.isValid() && localInput.isValid())) {
            return false;
        }
        units = normalizeUnits(!isUndefined(units) ? units : 'millisecond');
        if (units === 'millisecond') {
            return this.valueOf() < localInput.valueOf();
        } else {
            return this.clone().endOf(units).valueOf() < localInput.valueOf();
        }
    }

    function isBetween (from, to, units, inclusivity) {
        inclusivity = inclusivity || '()';
        return (inclusivity[0] === '(' ? this.isAfter(from, units) : !this.isBefore(from, units)) &&
            (inclusivity[1] === ')' ? this.isBefore(to, units) : !this.isAfter(to, units));
    }

    function isSame (input, units) {
        var localInput = isMoment(input) ? input : local__createLocal(input),
            inputMs;
        if (!(this.isValid() && localInput.isValid())) {
            return false;
        }
        units = normalizeUnits(units || 'millisecond');
        if (units === 'millisecond') {
            return this.valueOf() === localInput.valueOf();
        } else {
            inputMs = localInput.valueOf();
            return this.clone().startOf(units).valueOf() <= inputMs && inputMs <= this.clone().endOf(units).valueOf();
        }
    }

    function isSameOrAfter (input, units) {
        return this.isSame(input, units) || this.isAfter(input,units);
    }

    function isSameOrBefore (input, units) {
        return this.isSame(input, units) || this.isBefore(input,units);
    }

    function diff (input, units, asFloat) {
        var that,
            zoneDelta,
            delta, output;

        if (!this.isValid()) {
            return NaN;
        }

        that = cloneWithOffset(input, this);

        if (!that.isValid()) {
            return NaN;
        }

        zoneDelta = (that.utcOffset() - this.utcOffset()) * 6e4;

        units = normalizeUnits(units);

        if (units === 'year' || units === 'month' || units === 'quarter') {
            output = monthDiff(this, that);
            if (units === 'quarter') {
                output = output / 3;
            } else if (units === 'year') {
                output = output / 12;
            }
        } else {
            delta = this - that;
            output = units === 'second' ? delta / 1e3 : // 1000
                units === 'minute' ? delta / 6e4 : // 1000 * 60
                units === 'hour' ? delta / 36e5 : // 1000 * 60 * 60
                units === 'day' ? (delta - zoneDelta) / 864e5 : // 1000 * 60 * 60 * 24, negate dst
                units === 'week' ? (delta - zoneDelta) / 6048e5 : // 1000 * 60 * 60 * 24 * 7, negate dst
                delta;
        }
        return asFloat ? output : absFloor(output);
    }

    function monthDiff (a, b) {
        // difference in months
        var wholeMonthDiff = ((b.year() - a.year()) * 12) + (b.month() - a.month()),
            // b is in (anchor - 1 month, anchor + 1 month)
            anchor = a.clone().add(wholeMonthDiff, 'months'),
            anchor2, adjust;

        if (b - anchor < 0) {
            anchor2 = a.clone().add(wholeMonthDiff - 1, 'months');
            // linear across the month
            adjust = (b - anchor) / (anchor - anchor2);
        } else {
            anchor2 = a.clone().add(wholeMonthDiff + 1, 'months');
            // linear across the month
            adjust = (b - anchor) / (anchor2 - anchor);
        }

        //check for negative zero, return zero if negative zero
        return -(wholeMonthDiff + adjust) || 0;
    }

    utils_hooks__hooks.defaultFormat = 'YYYY-MM-DDTHH:mm:ssZ';
    utils_hooks__hooks.defaultFormatUtc = 'YYYY-MM-DDTHH:mm:ss[Z]';

    function toString () {
        return this.clone().locale('en').format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');
    }

    function moment_format__toISOString () {
        var m = this.clone().utc();
        if (0 < m.year() && m.year() <= 9999) {
            if (isFunction(Date.prototype.toISOString)) {
                // native implementation is ~50x faster, use it when we can
                return this.toDate().toISOString();
            } else {
                return formatMoment(m, 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]');
            }
        } else {
            return formatMoment(m, 'YYYYYY-MM-DD[T]HH:mm:ss.SSS[Z]');
        }
    }

    function format (inputString) {
        if (!inputString) {
            inputString = this.isUtc() ? utils_hooks__hooks.defaultFormatUtc : utils_hooks__hooks.defaultFormat;
        }
        var output = formatMoment(this, inputString);
        return this.localeData().postformat(output);
    }

    function from (time, withoutSuffix) {
        if (this.isValid() &&
                ((isMoment(time) && time.isValid()) ||
                 local__createLocal(time).isValid())) {
            return create__createDuration({to: this, from: time}).locale(this.locale()).humanize(!withoutSuffix);
        } else {
            return this.localeData().invalidDate();
        }
    }

    function fromNow (withoutSuffix) {
        return this.from(local__createLocal(), withoutSuffix);
    }

    function to (time, withoutSuffix) {
        if (this.isValid() &&
                ((isMoment(time) && time.isValid()) ||
                 local__createLocal(time).isValid())) {
            return create__createDuration({from: this, to: time}).locale(this.locale()).humanize(!withoutSuffix);
        } else {
            return this.localeData().invalidDate();
        }
    }

    function toNow (withoutSuffix) {
        return this.to(local__createLocal(), withoutSuffix);
    }

    // If passed a locale key, it will set the locale for this
    // instance.  Otherwise, it will return the locale configuration
    // variables for this instance.
    function locale (key) {
        var newLocaleData;

        if (key === undefined) {
            return this._locale._abbr;
        } else {
            newLocaleData = locale_locales__getLocale(key);
            if (newLocaleData != null) {
                this._locale = newLocaleData;
            }
            return this;
        }
    }

    var lang = deprecate(
        'moment().lang() is deprecated. Instead, use moment().localeData() to get the language configuration. Use moment().locale() to change languages.',
        function (key) {
            if (key === undefined) {
                return this.localeData();
            } else {
                return this.locale(key);
            }
        }
    );

    function localeData () {
        return this._locale;
    }

    function startOf (units) {
        units = normalizeUnits(units);
        // the following switch intentionally omits break keywords
        // to utilize falling through the cases.
        switch (units) {
            case 'year':
                this.month(0);
                /* falls through */
            case 'quarter':
            case 'month':
                this.date(1);
                /* falls through */
            case 'week':
            case 'isoWeek':
            case 'day':
            case 'date':
                this.hours(0);
                /* falls through */
            case 'hour':
                this.minutes(0);
                /* falls through */
            case 'minute':
                this.seconds(0);
                /* falls through */
            case 'second':
                this.milliseconds(0);
        }

        // weeks are a special case
        if (units === 'week') {
            this.weekday(0);
        }
        if (units === 'isoWeek') {
            this.isoWeekday(1);
        }

        // quarters are also special
        if (units === 'quarter') {
            this.month(Math.floor(this.month() / 3) * 3);
        }

        return this;
    }

    function endOf (units) {
        units = normalizeUnits(units);
        if (units === undefined || units === 'millisecond') {
            return this;
        }

        // 'date' is an alias for 'day', so it should be considered as such.
        if (units === 'date') {
            units = 'day';
        }

        return this.startOf(units).add(1, (units === 'isoWeek' ? 'week' : units)).subtract(1, 'ms');
    }

    function to_type__valueOf () {
        return this._d.valueOf() - ((this._offset || 0) * 60000);
    }

    function unix () {
        return Math.floor(this.valueOf() / 1000);
    }

    function toDate () {
        return new Date(this.valueOf());
    }

    function toArray () {
        var m = this;
        return [m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second(), m.millisecond()];
    }

    function toObject () {
        var m = this;
        return {
            years: m.year(),
            months: m.month(),
            date: m.date(),
            hours: m.hours(),
            minutes: m.minutes(),
            seconds: m.seconds(),
            milliseconds: m.milliseconds()
        };
    }

    function toJSON () {
        // new Date(NaN).toJSON() === null
        return this.isValid() ? this.toISOString() : null;
    }

    function moment_valid__isValid () {
        return valid__isValid(this);
    }

    function parsingFlags () {
        return extend({}, getParsingFlags(this));
    }

    function invalidAt () {
        return getParsingFlags(this).overflow;
    }

    function creationData() {
        return {
            input: this._i,
            format: this._f,
            locale: this._locale,
            isUTC: this._isUTC,
            strict: this._strict
        };
    }

    // FORMATTING

    addFormatToken(0, ['gg', 2], 0, function () {
        return this.weekYear() % 100;
    });

    addFormatToken(0, ['GG', 2], 0, function () {
        return this.isoWeekYear() % 100;
    });

    function addWeekYearFormatToken (token, getter) {
        addFormatToken(0, [token, token.length], 0, getter);
    }

    addWeekYearFormatToken('gggg',     'weekYear');
    addWeekYearFormatToken('ggggg',    'weekYear');
    addWeekYearFormatToken('GGGG',  'isoWeekYear');
    addWeekYearFormatToken('GGGGG', 'isoWeekYear');

    // ALIASES

    addUnitAlias('weekYear', 'gg');
    addUnitAlias('isoWeekYear', 'GG');

    // PRIORITY

    addUnitPriority('weekYear', 1);
    addUnitPriority('isoWeekYear', 1);


    // PARSING

    addRegexToken('G',      matchSigned);
    addRegexToken('g',      matchSigned);
    addRegexToken('GG',     match1to2, match2);
    addRegexToken('gg',     match1to2, match2);
    addRegexToken('GGGG',   match1to4, match4);
    addRegexToken('gggg',   match1to4, match4);
    addRegexToken('GGGGG',  match1to6, match6);
    addRegexToken('ggggg',  match1to6, match6);

    addWeekParseToken(['gggg', 'ggggg', 'GGGG', 'GGGGG'], function (input, week, config, token) {
        week[token.substr(0, 2)] = toInt(input);
    });

    addWeekParseToken(['gg', 'GG'], function (input, week, config, token) {
        week[token] = utils_hooks__hooks.parseTwoDigitYear(input);
    });

    // MOMENTS

    function getSetWeekYear (input) {
        return getSetWeekYearHelper.call(this,
                input,
                this.week(),
                this.weekday(),
                this.localeData()._week.dow,
                this.localeData()._week.doy);
    }

    function getSetISOWeekYear (input) {
        return getSetWeekYearHelper.call(this,
                input, this.isoWeek(), this.isoWeekday(), 1, 4);
    }

    function getISOWeeksInYear () {
        return weeksInYear(this.year(), 1, 4);
    }

    function getWeeksInYear () {
        var weekInfo = this.localeData()._week;
        return weeksInYear(this.year(), weekInfo.dow, weekInfo.doy);
    }

    function getSetWeekYearHelper(input, week, weekday, dow, doy) {
        var weeksTarget;
        if (input == null) {
            return weekOfYear(this, dow, doy).year;
        } else {
            weeksTarget = weeksInYear(input, dow, doy);
            if (week > weeksTarget) {
                week = weeksTarget;
            }
            return setWeekAll.call(this, input, week, weekday, dow, doy);
        }
    }

    function setWeekAll(weekYear, week, weekday, dow, doy) {
        var dayOfYearData = dayOfYearFromWeeks(weekYear, week, weekday, dow, doy),
            date = createUTCDate(dayOfYearData.year, 0, dayOfYearData.dayOfYear);

        this.year(date.getUTCFullYear());
        this.month(date.getUTCMonth());
        this.date(date.getUTCDate());
        return this;
    }

    // FORMATTING

    addFormatToken('Q', 0, 'Qo', 'quarter');

    // ALIASES

    addUnitAlias('quarter', 'Q');

    // PRIORITY

    addUnitPriority('quarter', 7);

    // PARSING

    addRegexToken('Q', match1);
    addParseToken('Q', function (input, array) {
        array[MONTH] = (toInt(input) - 1) * 3;
    });

    // MOMENTS

    function getSetQuarter (input) {
        return input == null ? Math.ceil((this.month() + 1) / 3) : this.month((input - 1) * 3 + this.month() % 3);
    }

    // FORMATTING

    addFormatToken('D', ['DD', 2], 'Do', 'date');

    // ALIASES

    addUnitAlias('date', 'D');

    // PRIOROITY
    addUnitPriority('date', 9);

    // PARSING

    addRegexToken('D',  match1to2);
    addRegexToken('DD', match1to2, match2);
    addRegexToken('Do', function (isStrict, locale) {
        return isStrict ? locale._ordinalParse : locale._ordinalParseLenient;
    });

    addParseToken(['D', 'DD'], DATE);
    addParseToken('Do', function (input, array) {
        array[DATE] = toInt(input.match(match1to2)[0], 10);
    });

    // MOMENTS

    var getSetDayOfMonth = makeGetSet('Date', true);

    // FORMATTING

    addFormatToken('DDD', ['DDDD', 3], 'DDDo', 'dayOfYear');

    // ALIASES

    addUnitAlias('dayOfYear', 'DDD');

    // PRIORITY
    addUnitPriority('dayOfYear', 4);

    // PARSING

    addRegexToken('DDD',  match1to3);
    addRegexToken('DDDD', match3);
    addParseToken(['DDD', 'DDDD'], function (input, array, config) {
        config._dayOfYear = toInt(input);
    });

    // HELPERS

    // MOMENTS

    function getSetDayOfYear (input) {
        var dayOfYear = Math.round((this.clone().startOf('day') - this.clone().startOf('year')) / 864e5) + 1;
        return input == null ? dayOfYear : this.add((input - dayOfYear), 'd');
    }

    // FORMATTING

    addFormatToken('m', ['mm', 2], 0, 'minute');

    // ALIASES

    addUnitAlias('minute', 'm');

    // PRIORITY

    addUnitPriority('minute', 14);

    // PARSING

    addRegexToken('m',  match1to2);
    addRegexToken('mm', match1to2, match2);
    addParseToken(['m', 'mm'], MINUTE);

    // MOMENTS

    var getSetMinute = makeGetSet('Minutes', false);

    // FORMATTING

    addFormatToken('s', ['ss', 2], 0, 'second');

    // ALIASES

    addUnitAlias('second', 's');

    // PRIORITY

    addUnitPriority('second', 15);

    // PARSING

    addRegexToken('s',  match1to2);
    addRegexToken('ss', match1to2, match2);
    addParseToken(['s', 'ss'], SECOND);

    // MOMENTS

    var getSetSecond = makeGetSet('Seconds', false);

    // FORMATTING

    addFormatToken('S', 0, 0, function () {
        return ~~(this.millisecond() / 100);
    });

    addFormatToken(0, ['SS', 2], 0, function () {
        return ~~(this.millisecond() / 10);
    });

    addFormatToken(0, ['SSS', 3], 0, 'millisecond');
    addFormatToken(0, ['SSSS', 4], 0, function () {
        return this.millisecond() * 10;
    });
    addFormatToken(0, ['SSSSS', 5], 0, function () {
        return this.millisecond() * 100;
    });
    addFormatToken(0, ['SSSSSS', 6], 0, function () {
        return this.millisecond() * 1000;
    });
    addFormatToken(0, ['SSSSSSS', 7], 0, function () {
        return this.millisecond() * 10000;
    });
    addFormatToken(0, ['SSSSSSSS', 8], 0, function () {
        return this.millisecond() * 100000;
    });
    addFormatToken(0, ['SSSSSSSSS', 9], 0, function () {
        return this.millisecond() * 1000000;
    });


    // ALIASES

    addUnitAlias('millisecond', 'ms');

    // PRIORITY

    addUnitPriority('millisecond', 16);

    // PARSING

    addRegexToken('S',    match1to3, match1);
    addRegexToken('SS',   match1to3, match2);
    addRegexToken('SSS',  match1to3, match3);

    var token;
    for (token = 'SSSS'; token.length <= 9; token += 'S') {
        addRegexToken(token, matchUnsigned);
    }

    function parseMs(input, array) {
        array[MILLISECOND] = toInt(('0.' + input) * 1000);
    }

    for (token = 'S'; token.length <= 9; token += 'S') {
        addParseToken(token, parseMs);
    }
    // MOMENTS

    var getSetMillisecond = makeGetSet('Milliseconds', false);

    // FORMATTING

    addFormatToken('z',  0, 0, 'zoneAbbr');
    addFormatToken('zz', 0, 0, 'zoneName');

    // MOMENTS

    function getZoneAbbr () {
        return this._isUTC ? 'UTC' : '';
    }

    function getZoneName () {
        return this._isUTC ? 'Coordinated Universal Time' : '';
    }

    var momentPrototype__proto = Moment.prototype;

    momentPrototype__proto.add               = add_subtract__add;
    momentPrototype__proto.calendar          = moment_calendar__calendar;
    momentPrototype__proto.clone             = clone;
    momentPrototype__proto.diff              = diff;
    momentPrototype__proto.endOf             = endOf;
    momentPrototype__proto.format            = format;
    momentPrototype__proto.from              = from;
    momentPrototype__proto.fromNow           = fromNow;
    momentPrototype__proto.to                = to;
    momentPrototype__proto.toNow             = toNow;
    momentPrototype__proto.get               = stringGet;
    momentPrototype__proto.invalidAt         = invalidAt;
    momentPrototype__proto.isAfter           = isAfter;
    momentPrototype__proto.isBefore          = isBefore;
    momentPrototype__proto.isBetween         = isBetween;
    momentPrototype__proto.isSame            = isSame;
    momentPrototype__proto.isSameOrAfter     = isSameOrAfter;
    momentPrototype__proto.isSameOrBefore    = isSameOrBefore;
    momentPrototype__proto.isValid           = moment_valid__isValid;
    momentPrototype__proto.lang              = lang;
    momentPrototype__proto.locale            = locale;
    momentPrototype__proto.localeData        = localeData;
    momentPrototype__proto.max               = prototypeMax;
    momentPrototype__proto.min               = prototypeMin;
    momentPrototype__proto.parsingFlags      = parsingFlags;
    momentPrototype__proto.set               = stringSet;
    momentPrototype__proto.startOf           = startOf;
    momentPrototype__proto.subtract          = add_subtract__subtract;
    momentPrototype__proto.toArray           = toArray;
    momentPrototype__proto.toObject          = toObject;
    momentPrototype__proto.toDate            = toDate;
    momentPrototype__proto.toISOString       = moment_format__toISOString;
    momentPrototype__proto.toJSON            = toJSON;
    momentPrototype__proto.toString          = toString;
    momentPrototype__proto.unix              = unix;
    momentPrototype__proto.valueOf           = to_type__valueOf;
    momentPrototype__proto.creationData      = creationData;

    // Year
    momentPrototype__proto.year       = getSetYear;
    momentPrototype__proto.isLeapYear = getIsLeapYear;

    // Week Year
    momentPrototype__proto.weekYear    = getSetWeekYear;
    momentPrototype__proto.isoWeekYear = getSetISOWeekYear;

    // Quarter
    momentPrototype__proto.quarter = momentPrototype__proto.quarters = getSetQuarter;

    // Month
    momentPrototype__proto.month       = getSetMonth;
    momentPrototype__proto.daysInMonth = getDaysInMonth;

    // Week
    momentPrototype__proto.week           = momentPrototype__proto.weeks        = getSetWeek;
    momentPrototype__proto.isoWeek        = momentPrototype__proto.isoWeeks     = getSetISOWeek;
    momentPrototype__proto.weeksInYear    = getWeeksInYear;
    momentPrototype__proto.isoWeeksInYear = getISOWeeksInYear;

    // Day
    momentPrototype__proto.date       = getSetDayOfMonth;
    momentPrototype__proto.day        = momentPrototype__proto.days             = getSetDayOfWeek;
    momentPrototype__proto.weekday    = getSetLocaleDayOfWeek;
    momentPrototype__proto.isoWeekday = getSetISODayOfWeek;
    momentPrototype__proto.dayOfYear  = getSetDayOfYear;

    // Hour
    momentPrototype__proto.hour = momentPrototype__proto.hours = getSetHour;

    // Minute
    momentPrototype__proto.minute = momentPrototype__proto.minutes = getSetMinute;

    // Second
    momentPrototype__proto.second = momentPrototype__proto.seconds = getSetSecond;

    // Millisecond
    momentPrototype__proto.millisecond = momentPrototype__proto.milliseconds = getSetMillisecond;

    // Offset
    momentPrototype__proto.utcOffset            = getSetOffset;
    momentPrototype__proto.utc                  = setOffsetToUTC;
    momentPrototype__proto.local                = setOffsetToLocal;
    momentPrototype__proto.parseZone            = setOffsetToParsedOffset;
    momentPrototype__proto.hasAlignedHourOffset = hasAlignedHourOffset;
    momentPrototype__proto.isDST                = isDaylightSavingTime;
    momentPrototype__proto.isLocal              = isLocal;
    momentPrototype__proto.isUtcOffset          = isUtcOffset;
    momentPrototype__proto.isUtc                = isUtc;
    momentPrototype__proto.isUTC                = isUtc;

    // Timezone
    momentPrototype__proto.zoneAbbr = getZoneAbbr;
    momentPrototype__proto.zoneName = getZoneName;

    // Deprecations
    momentPrototype__proto.dates  = deprecate('dates accessor is deprecated. Use date instead.', getSetDayOfMonth);
    momentPrototype__proto.months = deprecate('months accessor is deprecated. Use month instead', getSetMonth);
    momentPrototype__proto.years  = deprecate('years accessor is deprecated. Use year instead', getSetYear);
    momentPrototype__proto.zone   = deprecate('moment().zone is deprecated, use moment().utcOffset instead. http://momentjs.com/guides/#/warnings/zone/', getSetZone);
    momentPrototype__proto.isDSTShifted = deprecate('isDSTShifted is deprecated. See http://momentjs.com/guides/#/warnings/dst-shifted/ for more information', isDaylightSavingTimeShifted);

    var momentPrototype = momentPrototype__proto;

    function moment__createUnix (input) {
        return local__createLocal(input * 1000);
    }

    function moment__createInZone () {
        return local__createLocal.apply(null, arguments).parseZone();
    }

    function preParsePostFormat (string) {
        return string;
    }

    var prototype__proto = Locale.prototype;

    prototype__proto.calendar        = locale_calendar__calendar;
    prototype__proto.longDateFormat  = longDateFormat;
    prototype__proto.invalidDate     = invalidDate;
    prototype__proto.ordinal         = ordinal;
    prototype__proto.preparse        = preParsePostFormat;
    prototype__proto.postformat      = preParsePostFormat;
    prototype__proto.relativeTime    = relative__relativeTime;
    prototype__proto.pastFuture      = pastFuture;
    prototype__proto.set             = locale_set__set;

    // Month
    prototype__proto.months            =        localeMonths;
    prototype__proto.monthsShort       =        localeMonthsShort;
    prototype__proto.monthsParse       =        localeMonthsParse;
    prototype__proto.monthsRegex       = monthsRegex;
    prototype__proto.monthsShortRegex  = monthsShortRegex;

    // Week
    prototype__proto.week = localeWeek;
    prototype__proto.firstDayOfYear = localeFirstDayOfYear;
    prototype__proto.firstDayOfWeek = localeFirstDayOfWeek;

    // Day of Week
    prototype__proto.weekdays       =        localeWeekdays;
    prototype__proto.weekdaysMin    =        localeWeekdaysMin;
    prototype__proto.weekdaysShort  =        localeWeekdaysShort;
    prototype__proto.weekdaysParse  =        localeWeekdaysParse;

    prototype__proto.weekdaysRegex       =        weekdaysRegex;
    prototype__proto.weekdaysShortRegex  =        weekdaysShortRegex;
    prototype__proto.weekdaysMinRegex    =        weekdaysMinRegex;

    // Hours
    prototype__proto.isPM = localeIsPM;
    prototype__proto.meridiem = localeMeridiem;

    function lists__get (format, index, field, setter) {
        var locale = locale_locales__getLocale();
        var utc = create_utc__createUTC().set(setter, index);
        return locale[field](utc, format);
    }

    function listMonthsImpl (format, index, field) {
        if (typeof format === 'number') {
            index = format;
            format = undefined;
        }

        format = format || '';

        if (index != null) {
            return lists__get(format, index, field, 'month');
        }

        var i;
        var out = [];
        for (i = 0; i < 12; i++) {
            out[i] = lists__get(format, i, field, 'month');
        }
        return out;
    }

    // ()
    // (5)
    // (fmt, 5)
    // (fmt)
    // (true)
    // (true, 5)
    // (true, fmt, 5)
    // (true, fmt)
    function listWeekdaysImpl (localeSorted, format, index, field) {
        if (typeof localeSorted === 'boolean') {
            if (typeof format === 'number') {
                index = format;
                format = undefined;
            }

            format = format || '';
        } else {
            format = localeSorted;
            index = format;
            localeSorted = false;

            if (typeof format === 'number') {
                index = format;
                format = undefined;
            }

            format = format || '';
        }

        var locale = locale_locales__getLocale(),
            shift = localeSorted ? locale._week.dow : 0;

        if (index != null) {
            return lists__get(format, (index + shift) % 7, field, 'day');
        }

        var i;
        var out = [];
        for (i = 0; i < 7; i++) {
            out[i] = lists__get(format, (i + shift) % 7, field, 'day');
        }
        return out;
    }

    function lists__listMonths (format, index) {
        return listMonthsImpl(format, index, 'months');
    }

    function lists__listMonthsShort (format, index) {
        return listMonthsImpl(format, index, 'monthsShort');
    }

    function lists__listWeekdays (localeSorted, format, index) {
        return listWeekdaysImpl(localeSorted, format, index, 'weekdays');
    }

    function lists__listWeekdaysShort (localeSorted, format, index) {
        return listWeekdaysImpl(localeSorted, format, index, 'weekdaysShort');
    }

    function lists__listWeekdaysMin (localeSorted, format, index) {
        return listWeekdaysImpl(localeSorted, format, index, 'weekdaysMin');
    }

    locale_locales__getSetGlobalLocale('en', {
        ordinalParse: /\d{1,2}(th|st|nd|rd)/,
        ordinal : function (number) {
            var b = number % 10,
                output = (toInt(number % 100 / 10) === 1) ? 'th' :
                (b === 1) ? 'st' :
                (b === 2) ? 'nd' :
                (b === 3) ? 'rd' : 'th';
            return number + output;
        }
    });

    // Side effect imports
    utils_hooks__hooks.lang = deprecate('moment.lang is deprecated. Use moment.locale instead.', locale_locales__getSetGlobalLocale);
    utils_hooks__hooks.langData = deprecate('moment.langData is deprecated. Use moment.localeData instead.', locale_locales__getLocale);

    var mathAbs = Math.abs;

    function duration_abs__abs () {
        var data           = this._data;

        this._milliseconds = mathAbs(this._milliseconds);
        this._days         = mathAbs(this._days);
        this._months       = mathAbs(this._months);

        data.milliseconds  = mathAbs(data.milliseconds);
        data.seconds       = mathAbs(data.seconds);
        data.minutes       = mathAbs(data.minutes);
        data.hours         = mathAbs(data.hours);
        data.months        = mathAbs(data.months);
        data.years         = mathAbs(data.years);

        return this;
    }

    function duration_add_subtract__addSubtract (duration, input, value, direction) {
        var other = create__createDuration(input, value);

        duration._milliseconds += direction * other._milliseconds;
        duration._days         += direction * other._days;
        duration._months       += direction * other._months;

        return duration._bubble();
    }

    // supports only 2.0-style add(1, 's') or add(duration)
    function duration_add_subtract__add (input, value) {
        return duration_add_subtract__addSubtract(this, input, value, 1);
    }

    // supports only 2.0-style subtract(1, 's') or subtract(duration)
    function duration_add_subtract__subtract (input, value) {
        return duration_add_subtract__addSubtract(this, input, value, -1);
    }

    function absCeil (number) {
        if (number < 0) {
            return Math.floor(number);
        } else {
            return Math.ceil(number);
        }
    }

    function bubble () {
        var milliseconds = this._milliseconds;
        var days         = this._days;
        var months       = this._months;
        var data         = this._data;
        var seconds, minutes, hours, years, monthsFromDays;

        // if we have a mix of positive and negative values, bubble down first
        // check: https://github.com/moment/moment/issues/2166
        if (!((milliseconds >= 0 && days >= 0 && months >= 0) ||
                (milliseconds <= 0 && days <= 0 && months <= 0))) {
            milliseconds += absCeil(monthsToDays(months) + days) * 864e5;
            days = 0;
            months = 0;
        }

        // The following code bubbles up values, see the tests for
        // examples of what that means.
        data.milliseconds = milliseconds % 1000;

        seconds           = absFloor(milliseconds / 1000);
        data.seconds      = seconds % 60;

        minutes           = absFloor(seconds / 60);
        data.minutes      = minutes % 60;

        hours             = absFloor(minutes / 60);
        data.hours        = hours % 24;

        days += absFloor(hours / 24);

        // convert days to months
        monthsFromDays = absFloor(daysToMonths(days));
        months += monthsFromDays;
        days -= absCeil(monthsToDays(monthsFromDays));

        // 12 months -> 1 year
        years = absFloor(months / 12);
        months %= 12;

        data.days   = days;
        data.months = months;
        data.years  = years;

        return this;
    }

    function daysToMonths (days) {
        // 400 years have 146097 days (taking into account leap year rules)
        // 400 years have 12 months === 4800
        return days * 4800 / 146097;
    }

    function monthsToDays (months) {
        // the reverse of daysToMonths
        return months * 146097 / 4800;
    }

    function as (units) {
        var days;
        var months;
        var milliseconds = this._milliseconds;

        units = normalizeUnits(units);

        if (units === 'month' || units === 'year') {
            days   = this._days   + milliseconds / 864e5;
            months = this._months + daysToMonths(days);
            return units === 'month' ? months : months / 12;
        } else {
            // handle milliseconds separately because of floating point math errors (issue #1867)
            days = this._days + Math.round(monthsToDays(this._months));
            switch (units) {
                case 'week'   : return days / 7     + milliseconds / 6048e5;
                case 'day'    : return days         + milliseconds / 864e5;
                case 'hour'   : return days * 24    + milliseconds / 36e5;
                case 'minute' : return days * 1440  + milliseconds / 6e4;
                case 'second' : return days * 86400 + milliseconds / 1000;
                // Math.floor prevents floating point math errors here
                case 'millisecond': return Math.floor(days * 864e5) + milliseconds;
                default: throw new Error('Unknown unit ' + units);
            }
        }
    }

    // TODO: Use this.as('ms')?
    function duration_as__valueOf () {
        return (
            this._milliseconds +
            this._days * 864e5 +
            (this._months % 12) * 2592e6 +
            toInt(this._months / 12) * 31536e6
        );
    }

    function makeAs (alias) {
        return function () {
            return this.as(alias);
        };
    }

    var asMilliseconds = makeAs('ms');
    var asSeconds      = makeAs('s');
    var asMinutes      = makeAs('m');
    var asHours        = makeAs('h');
    var asDays         = makeAs('d');
    var asWeeks        = makeAs('w');
    var asMonths       = makeAs('M');
    var asYears        = makeAs('y');

    function duration_get__get (units) {
        units = normalizeUnits(units);
        return this[units + 's']();
    }

    function makeGetter(name) {
        return function () {
            return this._data[name];
        };
    }

    var milliseconds = makeGetter('milliseconds');
    var seconds      = makeGetter('seconds');
    var minutes      = makeGetter('minutes');
    var hours        = makeGetter('hours');
    var days         = makeGetter('days');
    var months       = makeGetter('months');
    var years        = makeGetter('years');

    function weeks () {
        return absFloor(this.days() / 7);
    }

    var round = Math.round;
    var thresholds = {
        s: 45,  // seconds to minute
        m: 45,  // minutes to hour
        h: 22,  // hours to day
        d: 26,  // days to month
        M: 11   // months to year
    };

    // helper function for moment.fn.from, moment.fn.fromNow, and moment.duration.fn.humanize
    function substituteTimeAgo(string, number, withoutSuffix, isFuture, locale) {
        return locale.relativeTime(number || 1, !!withoutSuffix, string, isFuture);
    }

    function duration_humanize__relativeTime (posNegDuration, withoutSuffix, locale) {
        var duration = create__createDuration(posNegDuration).abs();
        var seconds  = round(duration.as('s'));
        var minutes  = round(duration.as('m'));
        var hours    = round(duration.as('h'));
        var days     = round(duration.as('d'));
        var months   = round(duration.as('M'));
        var years    = round(duration.as('y'));

        var a = seconds < thresholds.s && ['s', seconds]  ||
                minutes <= 1           && ['m']           ||
                minutes < thresholds.m && ['mm', minutes] ||
                hours   <= 1           && ['h']           ||
                hours   < thresholds.h && ['hh', hours]   ||
                days    <= 1           && ['d']           ||
                days    < thresholds.d && ['dd', days]    ||
                months  <= 1           && ['M']           ||
                months  < thresholds.M && ['MM', months]  ||
                years   <= 1           && ['y']           || ['yy', years];

        a[2] = withoutSuffix;
        a[3] = +posNegDuration > 0;
        a[4] = locale;
        return substituteTimeAgo.apply(null, a);
    }

    // This function allows you to set the rounding function for relative time strings
    function duration_humanize__getSetRelativeTimeRounding (roundingFunction) {
        if (roundingFunction === undefined) {
            return round;
        }
        if (typeof(roundingFunction) === 'function') {
            round = roundingFunction;
            return true;
        }
        return false;
    }

    // This function allows you to set a threshold for relative time strings
    function duration_humanize__getSetRelativeTimeThreshold (threshold, limit) {
        if (thresholds[threshold] === undefined) {
            return false;
        }
        if (limit === undefined) {
            return thresholds[threshold];
        }
        thresholds[threshold] = limit;
        return true;
    }

    function humanize (withSuffix) {
        var locale = this.localeData();
        var output = duration_humanize__relativeTime(this, !withSuffix, locale);

        if (withSuffix) {
            output = locale.pastFuture(+this, output);
        }

        return locale.postformat(output);
    }

    var iso_string__abs = Math.abs;

    function iso_string__toISOString() {
        // for ISO strings we do not use the normal bubbling rules:
        //  * milliseconds bubble up until they become hours
        //  * days do not bubble at all
        //  * months bubble up until they become years
        // This is because there is no context-free conversion between hours and days
        // (think of clock changes)
        // and also not between days and months (28-31 days per month)
        var seconds = iso_string__abs(this._milliseconds) / 1000;
        var days         = iso_string__abs(this._days);
        var months       = iso_string__abs(this._months);
        var minutes, hours, years;

        // 3600 seconds -> 60 minutes -> 1 hour
        minutes           = absFloor(seconds / 60);
        hours             = absFloor(minutes / 60);
        seconds %= 60;
        minutes %= 60;

        // 12 months -> 1 year
        years  = absFloor(months / 12);
        months %= 12;


        // inspired by https://github.com/dordille/moment-isoduration/blob/master/moment.isoduration.js
        var Y = years;
        var M = months;
        var D = days;
        var h = hours;
        var m = minutes;
        var s = seconds;
        var total = this.asSeconds();

        if (!total) {
            // this is the same as C#'s (Noda) and python (isodate)...
            // but not other JS (goog.date)
            return 'P0D';
        }

        return (total < 0 ? '-' : '') +
            'P' +
            (Y ? Y + 'Y' : '') +
            (M ? M + 'M' : '') +
            (D ? D + 'D' : '') +
            ((h || m || s) ? 'T' : '') +
            (h ? h + 'H' : '') +
            (m ? m + 'M' : '') +
            (s ? s + 'S' : '');
    }

    var duration_prototype__proto = Duration.prototype;

    duration_prototype__proto.abs            = duration_abs__abs;
    duration_prototype__proto.add            = duration_add_subtract__add;
    duration_prototype__proto.subtract       = duration_add_subtract__subtract;
    duration_prototype__proto.as             = as;
    duration_prototype__proto.asMilliseconds = asMilliseconds;
    duration_prototype__proto.asSeconds      = asSeconds;
    duration_prototype__proto.asMinutes      = asMinutes;
    duration_prototype__proto.asHours        = asHours;
    duration_prototype__proto.asDays         = asDays;
    duration_prototype__proto.asWeeks        = asWeeks;
    duration_prototype__proto.asMonths       = asMonths;
    duration_prototype__proto.asYears        = asYears;
    duration_prototype__proto.valueOf        = duration_as__valueOf;
    duration_prototype__proto._bubble        = bubble;
    duration_prototype__proto.get            = duration_get__get;
    duration_prototype__proto.milliseconds   = milliseconds;
    duration_prototype__proto.seconds        = seconds;
    duration_prototype__proto.minutes        = minutes;
    duration_prototype__proto.hours          = hours;
    duration_prototype__proto.days           = days;
    duration_prototype__proto.weeks          = weeks;
    duration_prototype__proto.months         = months;
    duration_prototype__proto.years          = years;
    duration_prototype__proto.humanize       = humanize;
    duration_prototype__proto.toISOString    = iso_string__toISOString;
    duration_prototype__proto.toString       = iso_string__toISOString;
    duration_prototype__proto.toJSON         = iso_string__toISOString;
    duration_prototype__proto.locale         = locale;
    duration_prototype__proto.localeData     = localeData;

    // Deprecations
    duration_prototype__proto.toIsoString = deprecate('toIsoString() is deprecated. Please use toISOString() instead (notice the capitals)', iso_string__toISOString);
    duration_prototype__proto.lang = lang;

    // Side effect imports

    // FORMATTING

    addFormatToken('X', 0, 0, 'unix');
    addFormatToken('x', 0, 0, 'valueOf');

    // PARSING

    addRegexToken('x', matchSigned);
    addRegexToken('X', matchTimestamp);
    addParseToken('X', function (input, array, config) {
        config._d = new Date(parseFloat(input, 10) * 1000);
    });
    addParseToken('x', function (input, array, config) {
        config._d = new Date(toInt(input));
    });

    // Side effect imports


    utils_hooks__hooks.version = '2.14.1';

    setHookCallback(local__createLocal);

    utils_hooks__hooks.fn                    = momentPrototype;
    utils_hooks__hooks.min                   = min;
    utils_hooks__hooks.max                   = max;
    utils_hooks__hooks.now                   = now;
    utils_hooks__hooks.utc                   = create_utc__createUTC;
    utils_hooks__hooks.unix                  = moment__createUnix;
    utils_hooks__hooks.months                = lists__listMonths;
    utils_hooks__hooks.isDate                = isDate;
    utils_hooks__hooks.locale                = locale_locales__getSetGlobalLocale;
    utils_hooks__hooks.invalid               = valid__createInvalid;
    utils_hooks__hooks.duration              = create__createDuration;
    utils_hooks__hooks.isMoment              = isMoment;
    utils_hooks__hooks.weekdays              = lists__listWeekdays;
    utils_hooks__hooks.parseZone             = moment__createInZone;
    utils_hooks__hooks.localeData            = locale_locales__getLocale;
    utils_hooks__hooks.isDuration            = isDuration;
    utils_hooks__hooks.monthsShort           = lists__listMonthsShort;
    utils_hooks__hooks.weekdaysMin           = lists__listWeekdaysMin;
    utils_hooks__hooks.defineLocale          = defineLocale;
    utils_hooks__hooks.updateLocale          = updateLocale;
    utils_hooks__hooks.locales               = locale_locales__listLocales;
    utils_hooks__hooks.weekdaysShort         = lists__listWeekdaysShort;
    utils_hooks__hooks.normalizeUnits        = normalizeUnits;
    utils_hooks__hooks.relativeTimeRounding = duration_humanize__getSetRelativeTimeRounding;
    utils_hooks__hooks.relativeTimeThreshold = duration_humanize__getSetRelativeTimeThreshold;
    utils_hooks__hooks.calendarFormat        = getCalendarFormat;
    utils_hooks__hooks.prototype             = momentPrototype;

    var _moment = utils_hooks__hooks;

    return _moment;

}));
},{}],3:[function(require,module,exports){
(function(root, factory) {
    /* istanbul ignore next */
    if (typeof define === 'function' && define.amd) {
        // AMD. Register as an anonymous module.
        define([], factory);
    } else if (typeof exports === 'object') {
        // Node. Does not work with strict CommonJS, but
        // only CommonJS-like environments that support module.exports,
        // like Node.
        module.exports = factory();
    } else {
        // Browser globals (root is window)
        root.StringMask = factory();
    }
}(this, function() {
    var tokens = {
        '0': {pattern: /\d/, _default: '0'},
        '9': {pattern: /\d/, optional: true},
        '#': {pattern: /\d/, optional: true, recursive: true},
        'A': {pattern: /[a-zA-Z0-9]/},
        'S': {pattern: /[a-zA-Z]/},
        'U': {pattern: /[a-zA-Z]/, transform: function(c) { return c.toLocaleUpperCase(); }},
        'L': {pattern: /[a-zA-Z]/, transform: function(c) { return c.toLocaleLowerCase(); }},
        '$': {escape: true}
    };

    function isEscaped(pattern, pos) {
        var count = 0;
        var i = pos - 1;
        var token = {escape: true};
        while (i >= 0 && token && token.escape) {
            token = tokens[pattern.charAt(i)];
            count += token && token.escape ? 1 : 0;
            i--;
        }
        return count > 0 && count % 2 === 1;
    }

    function calcOptionalNumbersToUse(pattern, value) {
        var numbersInP = pattern.replace(/[^0]/g,'').length;
        var numbersInV = value.replace(/[^\d]/g,'').length;
        return numbersInV - numbersInP;
    }

    function concatChar(text, character, options, token) {
        if (token && typeof token.transform === 'function') {
            character = token.transform(character);
        }
        if (options.reverse) {
            return character + text;
        }
        return text + character;
    }

    function hasMoreTokens(pattern, pos, inc) {
        var pc = pattern.charAt(pos);
        var token = tokens[pc];
        if (pc === '') {
            return false;
        }
        return token && !token.escape ? true : hasMoreTokens(pattern, pos + inc, inc);
    }

    function hasMoreRecursiveTokens(pattern, pos, inc) {
        var pc = pattern.charAt(pos);
        var token = tokens[pc];
        if (pc === '') {
            return false;
        }
        return token && token.recursive ? true : hasMoreRecursiveTokens(pattern, pos + inc, inc);
    }

    function insertChar(text, char, position) {
        var t = text.split('');
        t.splice(position, 0, char);
        return t.join('');
    }

    function StringMask(pattern, opt) {
        this.options = opt || {};
        this.options = {
            reverse: this.options.reverse || false,
            usedefaults: this.options.usedefaults || this.options.reverse
        };
        this.pattern = pattern;
    }

    StringMask.prototype.process = function proccess(value) {
        if (!value) {
            return {result: '', valid: false};
        }
        value = value + '';
        var pattern2 = this.pattern;
        var valid = true;
        var formatted = '';
        var valuePos = this.options.reverse ? value.length - 1 : 0;
        var patternPos = 0;
        var optionalNumbersToUse = calcOptionalNumbersToUse(pattern2, value);
        var escapeNext = false;
        var recursive = [];
        var inRecursiveMode = false;

        var steps = {
            start: this.options.reverse ? pattern2.length - 1 : 0,
            end: this.options.reverse ? -1 : pattern2.length,
            inc: this.options.reverse ? -1 : 1
        };

        function continueCondition(options) {
            if (!inRecursiveMode && !recursive.length && hasMoreTokens(pattern2, patternPos, steps.inc)) {
                // continue in the normal iteration
                return true;
            } else if (!inRecursiveMode && recursive.length &&
                hasMoreRecursiveTokens(pattern2, patternPos, steps.inc)) {
                // continue looking for the recursive tokens
                // Note: all chars in the patterns after the recursive portion will be handled as static string
                return true;
            } else if (!inRecursiveMode) {
                // start to handle the recursive portion of the pattern
                inRecursiveMode = recursive.length > 0;
            }

            if (inRecursiveMode) {
                var pc = recursive.shift();
                recursive.push(pc);
                if (options.reverse && valuePos >= 0) {
                    patternPos++;
                    pattern2 = insertChar(pattern2, pc, patternPos);
                    return true;
                } else if (!options.reverse && valuePos < value.length) {
                    pattern2 = insertChar(pattern2, pc, patternPos);
                    return true;
                }
            }
            return patternPos < pattern2.length && patternPos >= 0;
        }

        /**
         * Iterate over the pattern's chars parsing/matching the input value chars
         * until the end of the pattern. If the pattern ends with recursive chars
         * the iteration will continue until the end of the input value.
         *
         * Note: The iteration must stop if an invalid char is found.
         */
        for (patternPos = steps.start; continueCondition(this.options); patternPos = patternPos + steps.inc) {
            // Value char
            var vc = value.charAt(valuePos);
            // Pattern char to match with the value char
            var pc = pattern2.charAt(patternPos);

            var token = tokens[pc];
            if (recursive.length && token && !token.recursive) {
                // In the recursive portion of the pattern: tokens not recursive must be seen as static chars
                token = null;
            }

            // 1. Handle escape tokens in pattern
            // go to next iteration: if the pattern char is a escape char or was escaped
            if (!inRecursiveMode || vc) {
                if (this.options.reverse && isEscaped(pattern2, patternPos)) {
                    // pattern char is escaped, just add it and move on
                    formatted = concatChar(formatted, pc, this.options, token);
                    // skip escape token
                    patternPos = patternPos + steps.inc;
                    continue;
                } else if (!this.options.reverse && escapeNext) {
                    // pattern char is escaped, just add it and move on
                    formatted = concatChar(formatted, pc, this.options, token);
                    escapeNext = false;
                    continue;
                } else if (!this.options.reverse && token && token.escape) {
                    // mark to escape the next pattern char
                    escapeNext = true;
                    continue;
                }
            }

            // 2. Handle recursive tokens in pattern
            // go to next iteration: if the value str is finished or
            //                       if there is a normal token in the recursive portion of the pattern
            if (!inRecursiveMode && token && token.recursive) {
                // save it to repeat in the end of the pattern and handle the value char now
                recursive.push(pc);
            } else if (inRecursiveMode && !vc) {
                // in recursive mode but value is finished. Add the pattern char if it is not a recursive token
                formatted = concatChar(formatted, pc, this.options, token);
                continue;
            } else if (!inRecursiveMode && recursive.length > 0 && !vc) {
                // recursiveMode not started but already in the recursive portion of the pattern
                continue;
            }

            // 3. Handle the value
            // break iterations: if value is invalid for the given pattern
            if (!token) {
                // add char of the pattern
                formatted = concatChar(formatted, pc, this.options, token);
                if (!inRecursiveMode && recursive.length) {
                    // save it to repeat in the end of the pattern
                    recursive.push(pc);
                }
            } else if (token.optional) {
                // if token is optional, only add the value char if it matchs the token pattern
                //                       if not, move on to the next pattern char
                if (token.pattern.test(vc) && optionalNumbersToUse) {
                    formatted = concatChar(formatted, vc, this.options, token);
                    valuePos = valuePos + steps.inc;
                    optionalNumbersToUse--;
                } else if (recursive.length > 0 && vc) {
                    valid = false;
                    break;
                }
            } else if (token.pattern.test(vc)) {
                // if token isn't optional the value char must match the token pattern
                formatted = concatChar(formatted, vc, this.options, token);
                valuePos = valuePos + steps.inc;
            } else if (!vc && token._default && this.options.usedefaults) {
                // if the token isn't optional and has a default value, use it if the value is finished
                formatted = concatChar(formatted, token._default, this.options, token);
            } else {
                // the string value don't match the given pattern
                valid = false;
                break;
            }
        }

        return {result: formatted, valid: valid};
    };

    StringMask.prototype.apply = function(value) {
        return this.process(value).result;
    };

    StringMask.prototype.validate = function(value) {
        return this.process(value).valid;
    };

    StringMask.process = function(value, pattern, options) {
        return new StringMask(pattern, options).process(value);
    };

    StringMask.apply = function(value, pattern, options) {
        return new StringMask(pattern, options).apply(value);
    };

    StringMask.validate = function(value, pattern, options) {
        return new StringMask(pattern, options).validate(value);
    };

    return StringMask;
}));

},{}],4:[function(require,module,exports){
'use strict';

module.exports = angular.module('ui.utils.masks', [
	require('./global/global-masks'),
	require('./br/br-masks'),
	require('./us/us-masks'),
	require('./ch/ch-masks')
]).name;

},{"./br/br-masks":6,"./ch/ch-masks":15,"./global/global-masks":19,"./us/us-masks":27}],5:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var maskFactory = require('mask-factory');

var boletoBancarioMask = new StringMask('00000.00000 00000.000000 00000.000000 0 00000000000000');

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.replace(/[^0-9]/g, '').slice(0, 47);
	},
	format: function(cleanValue) {
		if (cleanValue.length === 0) {
			return cleanValue;
		}

		return boletoBancarioMask.apply(cleanValue).replace(/[^0-9]$/, '');
	},
	validations: {
		brBoletoBancario: function(value) {
			return value.length === 47;
		}
	}
});

},{"mask-factory":"mask-factory","string-mask":3}],6:[function(require,module,exports){
'use strict';

var m = angular.module('ui.utils.masks.br', [
	require('../helpers'),
])
.directive('uiBrBoletoBancarioMask', require('./boleto-bancario/boleto-bancario'))
.directive('uiBrCepMask', require('./cep/cep'))
.directive('uiBrCnpjMask', require('./cnpj/cnpj'))
.directive('uiBrCpfMask', require('./cpf/cpf'))
.directive('uiBrCpfcnpjMask', require('./cpf-cnpj/cpf-cnpj'))
.directive('uiBrIeMask', require('./inscricao-estadual/ie'))
.directive('uiNfeAccessKeyMask', require('./nfe/nfe'))
.directive('uiBrCarPlateMask', require('./car-plate/car-plate'))
.directive('uiBrPhoneNumber', require('./phone/br-phone'));

module.exports = m.name;

},{"../helpers":25,"./boleto-bancario/boleto-bancario":5,"./car-plate/car-plate":7,"./cep/cep":8,"./cnpj/cnpj":9,"./cpf-cnpj/cpf-cnpj":10,"./cpf/cpf":11,"./inscricao-estadual/ie":12,"./nfe/nfe":13,"./phone/br-phone":14}],7:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var maskFactory = require('mask-factory');

var carPlateMask = new StringMask('UUU-0000');

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.replace(/[^a-zA-Z0-9]/g, '').slice(0, 7);
	},
	format: function(cleanValue) {
		return (carPlateMask.apply(cleanValue) || '').replace(/[^a-zA-Z0-9]$/, '');
	},
	validations: {
		carPlate: function(value) {
			return value.length === 7;
		}
	}
});

},{"mask-factory":"mask-factory","string-mask":3}],8:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var maskFactory = require('mask-factory');

var cepMask = new StringMask('00000-000');

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.toString().replace(/[^0-9]/g, '').slice(0, 8);
	},
	format: function(cleanValue) {
		return (cepMask.apply(cleanValue) || '').replace(/[^0-9]$/, '');
	},
	validations: {
		cep: function(value) {
			return value.length === 8;
		}
	}
});

},{"mask-factory":"mask-factory","string-mask":3}],9:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var BrV = require('br-validations');
var maskFactory = require('mask-factory');

var cnpjPattern = new StringMask('00.000.000\/0000-00');

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.replace(/[^\d]/g, '').slice(0, 14);
	},
	format: function(cleanValue) {
		return (cnpjPattern.apply(cleanValue) || '').trim().replace(/[^0-9]$/, '');
	},
	validations: {
		cnpj: function(value) {
			return BrV.cnpj.validate(value);
		}
	}
});

},{"br-validations":1,"mask-factory":"mask-factory","string-mask":3}],10:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var BrV = require('br-validations');
var maskFactory = require('mask-factory');

var cnpjPattern = new StringMask('00.000.000\/0000-00');
var cpfPattern = new StringMask('000.000.000-00');

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.replace(/[^\d]/g, '').slice(0, 14);
	},
	format: function(cleanValue) {
		var formatedValue;

		if (cleanValue.length > 11) {
			formatedValue = cnpjPattern.apply(cleanValue);
		} else {
			formatedValue = cpfPattern.apply(cleanValue) || '';
		}

		return formatedValue.trim().replace(/[^0-9]$/, '');
	},
	validations: {
		cpf: function(value) {
			return value.length > 11 || BrV.cpf.validate(value);
		},
		cnpj: function(value) {
			return value.length <= 11 || BrV.cnpj.validate(value);
		}
	}
});

},{"br-validations":1,"mask-factory":"mask-factory","string-mask":3}],11:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var BrV = require('br-validations');
var maskFactory = require('mask-factory');

var cpfPattern = new StringMask('000.000.000-00');

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.replace(/[^\d]/g, '').slice(0, 11);
	},
	format: function(cleanValue) {
		return (cpfPattern.apply(cleanValue) || '').trim().replace(/[^0-9]$/, '');
	},
	validations: {
		cpf: function(value) {
			return BrV.cpf.validate(value);
		}
	}
});

},{"br-validations":1,"mask-factory":"mask-factory","string-mask":3}],12:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var BrV = require('br-validations');

function BrIeMaskDirective($parse) {
	var ieMasks = {
		'AC': [{mask: new StringMask('00.000.000/000-00')}],
		'AL': [{mask: new StringMask('000000000')}],
		'AM': [{mask: new StringMask('00.000.000-0')}],
		'AP': [{mask: new StringMask('000000000')}],
		'BA': [{chars: 8, mask: new StringMask('000000-00')},
			{mask: new StringMask('0000000-00')}],
		'CE': [{mask: new StringMask('00000000-0')}],
		'DF': [{mask: new StringMask('00000000000-00')}],
		'ES': [{mask: new StringMask('00000000-0')}],
		'GO': [{mask: new StringMask('00.000.000-0')}],
		'MA': [{mask: new StringMask('000000000')}],
		'MG': [{mask: new StringMask('000.000.000/0000')}],
		'MS': [{mask: new StringMask('000000000')}],
		'MT': [{mask: new StringMask('0000000000-0')}],
		'PA': [{mask: new StringMask('00-000000-0')}],
		'PB': [{mask: new StringMask('00000000-0')}],
		'PE': [{chars: 9, mask: new StringMask('0000000-00')},
			{mask: new StringMask('00.0.000.0000000-0')}],
		'PI': [{mask: new StringMask('000000000')}],
		'PR': [{mask: new StringMask('000.00000-00')}],
		'RJ': [{mask: new StringMask('00.000.00-0')}],
		'RN': [{chars: 9, mask: new StringMask('00.000.000-0')},
			{mask: new StringMask('00.0.000.000-0')}],
		'RO': [{mask: new StringMask('0000000000000-0')}],
		'RR': [{mask: new StringMask('00000000-0')}],
		'RS': [{mask: new StringMask('000/0000000')}],
		'SC': [{mask: new StringMask('000.000.000')}],
		'SE': [{mask: new StringMask('00000000-0')}],
		'SP': [{mask: new StringMask('000.000.000.000')},
			{mask: new StringMask('-00000000.0/000')}],
		'TO': [{mask: new StringMask('00000000000')}]
	};

	function clearValue(value) {
		if (!value) {
			return value;
		}

		return value.replace(/[^0-9]/g, '');
	}

	function getMask(uf, value) {
		if (!uf || !ieMasks[uf]) {
			return;
		}

		if (uf === 'SP' && /^P/i.test(value)) {
			return ieMasks.SP[1].mask;
		}

		var masks = ieMasks[uf];
		var i = 0;
		while (masks[i].chars && masks[i].chars < clearValue(value).length && i < masks.length - 1) {
			i++;
		}

		return masks[i].mask;
	}

	function applyIEMask(value, uf) {
		var mask = getMask(uf, value);

		if (!mask) {
			return value;
		}

		var processed = mask.process(clearValue(value));
		var formatedValue = processed.result || '';
		formatedValue = formatedValue.trim().replace(/[^0-9]$/, '');

		if (uf === 'SP' && /^p/i.test(value)) {
			return 'P' + formatedValue;
		}

		return formatedValue;
	}

	return {
		restrict: 'A',
		require: 'ngModel',
		link: function(scope, element, attrs, ctrl) {
			var state = ($parse(attrs.uiBrIeMask)(scope) || '').toUpperCase();

			function formatter(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				return applyIEMask(value, state);
			}

			function parser(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				var formatedValue = applyIEMask(value, state);
				var actualValue = clearValue(formatedValue);

				if (ctrl.$viewValue !== formatedValue) {
					ctrl.$setViewValue(formatedValue);
					ctrl.$render();
				}

				if (state && state.toUpperCase() === 'SP' && /^p/i.test(value)) {
					return 'P' + actualValue;
				}

				return actualValue;
			}

			ctrl.$formatters.push(formatter);
			ctrl.$parsers.push(parser);

			ctrl.$validators.ie = function validator(modelValue) {
				return ctrl.$isEmpty(modelValue) || BrV.ie(state).validate(modelValue);
			};

			scope.$watch(attrs.uiBrIeMask, function(newState) {
				state = (newState || '').toUpperCase();

				parser(ctrl.$viewValue);
				ctrl.$validate();
			});
		}
	};
}
BrIeMaskDirective.$inject = ['$parse'];

module.exports = BrIeMaskDirective;

},{"br-validations":1,"string-mask":3}],13:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var maskFactory = require('mask-factory');

var nfeAccessKeyMask = new StringMask('0000 0000 0000 0000 0000' +
	' 0000 0000 0000 0000 0000 0000');

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.replace(/[^0-9]/g, '').slice(0, 44);
	},
	format: function(cleanValue) {
		return (nfeAccessKeyMask.apply(cleanValue) || '').replace(/[^0-9]$/, '');
	},
	validations: {
		nfeAccessKey: function(value) {
			return value.length === 44;
		}
	}
});

},{"mask-factory":"mask-factory","string-mask":3}],14:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var maskFactory = require('mask-factory');

/**
 * FIXME: all numbers will have 9 digits after 2016.
 * see http://portal.embratel.com.br/embratel/9-digito/
 */
var phoneMask8D = {
		areaCode: new StringMask('(00) 0000-0000'), 	//with area code
		simple: new StringMask('0000-0000') 			//without area code
	}, phoneMask9D = {
		areaCode: new StringMask('(00) 00000-0000'), 	//with area code
		simple: new StringMask('00000-0000') 			//without area code
	}, phoneMask0800 = {
		areaCode: null,									//N/A
		simple: new StringMask('0000-000-0000') 		//N/A, so it's "simple"
	};

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.toString().replace(/[^0-9]/g, '').slice(0, 11);
	},
	format: function(cleanValue) {
		var formattedValue;

		if (cleanValue.indexOf('0800') === 0) {
			formattedValue = phoneMask0800.simple.apply(cleanValue);
		} else if (cleanValue.length < 9) {
			formattedValue = phoneMask8D.simple.apply(cleanValue) || '';
		} else if (cleanValue.length < 10) {
			formattedValue = phoneMask9D.simple.apply(cleanValue);
		} else if (cleanValue.length < 11) {
			formattedValue = phoneMask8D.areaCode.apply(cleanValue);
		} else {
			formattedValue = phoneMask9D.areaCode.apply(cleanValue);
		}

		return formattedValue.trim().replace(/[^0-9]$/, '');
	},
	getModelValue: function(formattedValue, originalModelType) {
		var cleanValue = this.clearValue(formattedValue);
		return originalModelType === 'number' ? parseInt(cleanValue) : cleanValue;
	},
	validations: {
		brPhoneNumber: function(value) {
			var valueLength = value && value.toString().length;

			//8- 8D without DD
			//9- 9D without DD
			//10- 9D with DD
			//11- 8D with DD and 0800
			return valueLength >= 8 && valueLength <= 11;
		}
	}
});

},{"mask-factory":"mask-factory","string-mask":3}],15:[function(require,module,exports){
'use strict';

var m = angular.module('ui.utils.masks.ch', [
	require('../helpers'),
])
.directive('uiChPhoneNumber', require('./phone/ch-phone'));

module.exports = m.name;

},{"../helpers":25,"./phone/ch-phone":16}],16:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var maskFactory = require('mask-factory');

var phoneMask = new StringMask('+00 00 000 00 00');

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.toString().replace(/[^0-9]/g, '').slice(0, 11);
	},
	format: function(cleanValue) {
		var formatedValue;

		formatedValue = phoneMask.apply(cleanValue) || '';

		return formatedValue.trim().replace(/[^0-9]$/, '');
	},
	validations: {
		chPhoneNumber: function(value) {
			var valueLength = value && value.toString().length;
			return valueLength === 11;
		}
	}
});

},{"mask-factory":"mask-factory","string-mask":3}],17:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var maskFactory = require('mask-factory');

var ccSize = 16;

var ccMask = new StringMask('0000 0000 0000 0000');

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.toString().replace(/[^0-9]/g, '').slice(0, ccSize);
	},
	format: function(cleanValue) {
		var formatedValue;

		formatedValue = ccMask.apply(cleanValue) || '';

		return formatedValue.trim().replace(/[^0-9]$/, '');
	},
	validations: {
		creditCard: function(value) {
			var valueLength = value && value.toString().length;
			return valueLength === ccSize;
		}
	}
});

},{"mask-factory":"mask-factory","string-mask":3}],18:[function(require,module,exports){
'use strict';

var moment = require('moment');
var StringMask = require('string-mask');

function isISODateString(date) {
	return /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\.[0-9]{3}([-+][0-9]{2}:[0-9]{2}|Z)$/
		.test(date.toString());
}

function DateMaskDirective($locale) {
	var dateFormatMapByLocale = {
		'pt-br': 'DD/MM/YYYY',
		'ru': 'DD.MM.YYYY',
	};

	var dateFormat = dateFormatMapByLocale[$locale.id] || 'YYYY-MM-DD';

	return {
		restrict: 'A',
		require: 'ngModel',
		link: function(scope, element, attrs, ctrl) {
			attrs.parse = attrs.parse || 'true';

			dateFormat = attrs.uiDateMask || dateFormat;

			var dateMask = new StringMask(dateFormat.replace(/[YMD]/g,'0'));

			function formatter(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				var cleanValue = value;
				if (typeof value === 'object' || isISODateString(value)) {
					cleanValue = moment(value).format(dateFormat);
				}

				cleanValue = cleanValue.replace(/[^0-9]/g, '');
				var formatedValue = dateMask.apply(cleanValue) || '';

				return formatedValue.trim().replace(/[^0-9]$/, '');
			}

			ctrl.$formatters.push(formatter);

			ctrl.$parsers.push(function parser(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				var formatedValue = formatter(value);

				if (ctrl.$viewValue !== formatedValue) {
					ctrl.$setViewValue(formatedValue);
					ctrl.$render();
				}

				return attrs.parse === 'false'
					? formatedValue
					: moment(formatedValue, dateFormat).toDate();
			});

			ctrl.$validators.date =	function validator(modelValue, viewValue) {
				if (ctrl.$isEmpty(modelValue)) {
					return true;
				}

				return moment(viewValue, dateFormat).isValid() && viewValue.length === dateFormat.length;
			};
		}
	};
}
DateMaskDirective.$inject = ['$locale'];

module.exports = DateMaskDirective;

},{"moment":2,"string-mask":3}],19:[function(require,module,exports){
'use strict';

var m = angular.module('ui.utils.masks.global', [
	require('../helpers'),
])
.directive('uiDateMask', require('./date/date'))
.directive('uiMoneyMask', require('./money/money'))
.directive('uiNumberMask', require('./number/number'))
.directive('uiPercentageMask', require('./percentage/percentage'))
.directive('uiScientificNotationMask', require('./scientific-notation/scientific-notation'))
.directive('uiTimeMask', require('./time/time'))
.directive('uiCreditCard', require('./credit-card/credit-card'));

module.exports = m.name;

},{"../helpers":25,"./credit-card/credit-card":17,"./date/date":18,"./money/money":20,"./number/number":21,"./percentage/percentage":22,"./scientific-notation/scientific-notation":23,"./time/time":24}],20:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var validators = require('validators');

function MoneyMaskDirective($locale, $parse, PreFormatters) {
	return {
		restrict: 'A',
		require: 'ngModel',
		link: function(scope, element, attrs, ctrl) {
			var decimalDelimiter = $locale.NUMBER_FORMATS.DECIMAL_SEP,
				thousandsDelimiter = $locale.NUMBER_FORMATS.GROUP_SEP,
				currencySym = $locale.NUMBER_FORMATS.CURRENCY_SYM,
				symbolSeparation = ' ',
				decimals = $parse(attrs.uiMoneyMask)(scope),
				backspacePressed = false;

			element.bind('keydown keypress', function(event) {
				backspacePressed = event.which === 8;
			});

			function maskFactory(decimals) {
				var decimalsPattern = decimals > 0 ? decimalDelimiter + new Array(decimals + 1).join('0') : '';
				var maskPattern =  '#' + thousandsDelimiter + '##0' + decimalsPattern;
				if (angular.isDefined(attrs.uiCurrencyAfter)) {
					maskPattern += symbolSeparation;
				} else {
					maskPattern =  symbolSeparation + maskPattern;
				}
				return new StringMask(maskPattern, {reverse: true});
			}

			if (angular.isDefined(attrs.uiDecimalDelimiter)) {
				decimalDelimiter = attrs.uiDecimalDelimiter;
			}

			if (angular.isDefined(attrs.uiThousandsDelimiter)) {
				thousandsDelimiter = attrs.uiThousandsDelimiter;
			}

			if (angular.isDefined(attrs.uiHideGroupSep)) {
				thousandsDelimiter = '';
			}

			if (angular.isDefined(attrs.uiHideSpace)) {
				symbolSeparation = '';
			}

			if (angular.isDefined(attrs.currencySymbol)) {
				currencySym = attrs.currencySymbol;
				if (attrs.currencySymbol.length === 0) {
					symbolSeparation = '';
				}
			}

			if (isNaN(decimals)) {
				decimals = 2;
			}
			decimals = parseInt(decimals);
			var moneyMask = maskFactory(decimals);

			function formatter(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}
				var prefix = (angular.isDefined(attrs.uiNegativeNumber) && value < 0) ? '-' : '';
				var valueToFormat = PreFormatters.prepareNumberToFormatter(value, decimals);
				if (angular.isDefined(attrs.uiCurrencyAfter)) {
					return prefix + moneyMask.apply(valueToFormat) + currencySym;
				}
				return prefix + currencySym + moneyMask.apply(valueToFormat);
			}

			function parser(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				var actualNumber = value.replace(/[^\d]+/g,''), formatedValue;
				actualNumber = actualNumber.replace(/^[0]+([1-9])/,'$1');
				actualNumber = actualNumber || '0';

				if (backspacePressed && angular.isDefined(attrs.uiCurrencyAfter) && actualNumber !== 0) {
					actualNumber = actualNumber.substring(0, actualNumber.length - 1);
					backspacePressed = false;
				}
				if (angular.isDefined(attrs.uiCurrencyAfter)) {
					formatedValue = moneyMask.apply(actualNumber) + currencySym;
				} else {
					formatedValue = currencySym + moneyMask.apply(actualNumber);
				}

				if (angular.isDefined(attrs.uiNegativeNumber)) {
					var isNegative = (value[0] === '-'),
						needsToInvertSign = (value.slice(-1) === '-');

					//only apply the minus sign if it is negative or(exclusive)
					//needs to be negative and the number is different from zero
					if (needsToInvertSign ^ isNegative && !!actualNumber) {
						actualNumber *= -1;
						formatedValue = '-' + formatedValue;
					}
				}

				if (value !== formatedValue) {
					ctrl.$setViewValue(formatedValue);
					ctrl.$render();
				}

				return formatedValue ? parseInt(formatedValue.replace(/[^\d\-]+/g,''))/Math.pow(10,decimals) : null;
			}

			ctrl.$formatters.push(formatter);
			ctrl.$parsers.push(parser);

			if (attrs.uiMoneyMask) {
				scope.$watch(attrs.uiMoneyMask, function(_decimals) {
					decimals = isNaN(_decimals) ? 2 : _decimals;
					decimals = parseInt(decimals);
					moneyMask = maskFactory(decimals);

					parser(ctrl.$viewValue);
				});
			}

			if (attrs.min) {
				var minVal;

				ctrl.$validators.min = function(modelValue) {
					return validators.minNumber(ctrl, modelValue, minVal);
				};

				scope.$watch(attrs.min, function(value) {
					minVal = value;
					ctrl.$validate();
				});
			}

			if (attrs.max) {
				var maxVal;

				ctrl.$validators.max = function(modelValue) {
					return validators.maxNumber(ctrl, modelValue, maxVal);
				};

				scope.$watch(attrs.max, function(value) {
					maxVal = value;
					ctrl.$validate();
				});
			}
		}
	};
}
MoneyMaskDirective.$inject = ['$locale', '$parse', 'PreFormatters'];

module.exports = MoneyMaskDirective;

},{"string-mask":3,"validators":"validators"}],21:[function(require,module,exports){
'use strict';

var validators = require('validators');

function NumberMaskDirective($locale, $parse, PreFormatters, NumberMasks) {
	return {
		restrict: 'A',
		require: 'ngModel',
		link: function(scope, element, attrs, ctrl) {
			var decimalDelimiter = $locale.NUMBER_FORMATS.DECIMAL_SEP,
				thousandsDelimiter = $locale.NUMBER_FORMATS.GROUP_SEP,
				decimals = $parse(attrs.uiNumberMask)(scope);

			if (angular.isDefined(attrs.uiHideGroupSep)) {
				thousandsDelimiter = '';
			}

			if (isNaN(decimals)) {
				decimals = 2;
			}

			var viewMask = NumberMasks.viewMask(decimals, decimalDelimiter, thousandsDelimiter),
				modelMask = NumberMasks.modelMask(decimals);

			function parser(value) {
				if (ctrl.$isEmpty(value)) {
					return null;
				}

				var valueToFormat = PreFormatters.clearDelimitersAndLeadingZeros(value) || '0';
				var formatedValue = viewMask.apply(valueToFormat);
				var actualNumber = parseFloat(modelMask.apply(valueToFormat));

				if (angular.isDefined(attrs.uiNegativeNumber)) {
					var isNegative = (value[0] === '-'),
						needsToInvertSign = (value.slice(-1) === '-');

					//only apply the minus sign if it is negative or(exclusive) or the first character
					//needs to be negative and the number is different from zero
					if ((needsToInvertSign ^ isNegative) || value === '-') {
						actualNumber *= -1;
						formatedValue = '-' + ((actualNumber !== 0) ? formatedValue : '');
					}
				}

				if (ctrl.$viewValue !== formatedValue) {
					ctrl.$setViewValue(formatedValue);
					ctrl.$render();
				}

				return actualNumber;
			}

			function formatter(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				var prefix = (angular.isDefined(attrs.uiNegativeNumber) && value < 0) ? '-' : '';
				var valueToFormat = PreFormatters.prepareNumberToFormatter(value, decimals);
				return prefix + viewMask.apply(valueToFormat);
			}

			function clearViewValueIfMinusSign() {
				if (ctrl.$viewValue === '-') {
					ctrl.$setViewValue('');
					ctrl.$render();
				}
			}

			element.on('blur', clearViewValueIfMinusSign);

			ctrl.$formatters.push(formatter);
			ctrl.$parsers.push(parser);

			if (attrs.uiNumberMask) {
				scope.$watch(attrs.uiNumberMask, function(_decimals) {
					decimals = isNaN(_decimals) ? 2 : _decimals;
					viewMask = NumberMasks.viewMask(decimals, decimalDelimiter, thousandsDelimiter);
					modelMask = NumberMasks.modelMask(decimals);

					parser(ctrl.$viewValue);
				});
			}

			if (attrs.min) {
				var minVal;

				ctrl.$validators.min = function(modelValue) {
					return validators.minNumber(ctrl, modelValue, minVal);
				};

				scope.$watch(attrs.min, function(value) {
					minVal = value;
					ctrl.$validate();
				});
			}

			if (attrs.max) {
				var maxVal;

				ctrl.$validators.max = function(modelValue) {
					return validators.maxNumber(ctrl, modelValue, maxVal);
				};

				scope.$watch(attrs.max, function(value) {
					maxVal = value;
					ctrl.$validate();
				});
			}
		}
	};
}
NumberMaskDirective.$inject = ['$locale', '$parse', 'PreFormatters', 'NumberMasks'];

module.exports = NumberMaskDirective;

},{"validators":"validators"}],22:[function(require,module,exports){
'use strict';

var validators = require('validators');

function PercentageMaskDirective($locale, $parse, PreFormatters, NumberMasks) {
	function preparePercentageToFormatter(value, decimals, modelMultiplier) {
		return PreFormatters.clearDelimitersAndLeadingZeros((parseFloat(value)*modelMultiplier).toFixed(decimals));
	}

	return {
		restrict: 'A',
		require: 'ngModel',
		link: function(scope, element, attrs, ctrl) {
			var decimalDelimiter = $locale.NUMBER_FORMATS.DECIMAL_SEP,
				thousandsDelimiter = $locale.NUMBER_FORMATS.GROUP_SEP,
				decimals = parseInt(attrs.uiPercentageMask),
				hideSpace = false,
				backspacePressed = false;

			element.bind('keydown keypress', function(event) {
				backspacePressed = event.which === 8;
			});

			var modelValue = {
				multiplier : 100,
				decimalMask: 2
			};

			if (angular.isDefined(attrs.uiHideGroupSep)) {
				thousandsDelimiter = '';
			}

			if (angular.isDefined(attrs.uiHideSpace)) {
				hideSpace = true;
			}

			if (angular.isDefined(attrs.uiPercentageValue)) {
				modelValue.multiplier  = 1;
				modelValue.decimalMask = 0;
			}

			if (isNaN(decimals)) {
				decimals = 2;
			}

			var numberDecimals = decimals + modelValue.decimalMask;
			var viewMask = NumberMasks.viewMask(decimals, decimalDelimiter, thousandsDelimiter),
				modelMask = NumberMasks.modelMask(numberDecimals);

			function formatter(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				var valueToFormat = preparePercentageToFormatter(value, decimals, modelValue.multiplier);
				return viewMask.apply(valueToFormat) + (hideSpace ? '%' : ' %');
			}

			function parse(value) {
				if (ctrl.$isEmpty(value)) {
					return null;
				}

				var valueToFormat = PreFormatters.clearDelimitersAndLeadingZeros(value) || '0';
				if (value.length > 1 && value.indexOf('%') === -1) {
					valueToFormat = valueToFormat.slice(0,valueToFormat.length-1);
				}
				if (backspacePressed && value.length === 1 && value !== '%') {
					valueToFormat = '0';
				}
				var percentSign = hideSpace ? '%' : ' %';
				var formatedValue = viewMask.apply(valueToFormat) + percentSign;
				var actualNumber = parseFloat(modelMask.apply(valueToFormat));

				if (ctrl.$viewValue !== formatedValue) {
					ctrl.$setViewValue(formatedValue);
					ctrl.$render();
				}

				return actualNumber;
			}

			ctrl.$formatters.push(formatter);
			ctrl.$parsers.push(parse);

			if (attrs.uiPercentageMask) {
				scope.$watch(attrs.uiPercentageMask, function(_decimals) {
					decimals = isNaN(_decimals) ? 2 : _decimals;

					if (angular.isDefined(attrs.uiPercentageValue)) {
						modelValue.multiplier  = 1;
						modelValue.decimalMask = 0;
					}

					numberDecimals = decimals + modelValue.decimalMask;
					viewMask = NumberMasks.viewMask(decimals, decimalDelimiter, thousandsDelimiter);
					modelMask = NumberMasks.modelMask(numberDecimals);

					parse(ctrl.$viewValue);
				});
			}

			if (attrs.min) {
				var minVal;

				ctrl.$validators.min = function(modelValue) {
					return validators.minNumber(ctrl, modelValue, minVal);
				};

				scope.$watch(attrs.min, function(value) {
					minVal = value;
					ctrl.$validate();
				});
			}

			if (attrs.max) {
				var maxVal;

				ctrl.$validators.max = function(modelValue) {
					return validators.maxNumber(ctrl, modelValue, maxVal);
				};

				scope.$watch(attrs.max, function(value) {
					maxVal = value;
					ctrl.$validate();
				});
			}
		}
	};
}
PercentageMaskDirective.$inject = ['$locale', '$parse', 'PreFormatters', 'NumberMasks'];

module.exports = PercentageMaskDirective;

},{"validators":"validators"}],23:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');

function ScientificNotationMaskDirective($locale, $parse) {
	var decimalDelimiter = $locale.NUMBER_FORMATS.DECIMAL_SEP,
		defaultPrecision = 2;

	function significandMaskBuilder(decimals) {
		var mask = '0';

		if (decimals > 0) {
			mask += decimalDelimiter;
			for (var i = 0; i < decimals; i++) {
				mask += '0';
			}
		}

		return new StringMask(mask, {
			reverse: true
		});
	}

	return {
		restrict: 'A',
		require: 'ngModel',
		link: function(scope, element, attrs, ctrl) {
			var decimals = $parse(attrs.uiScientificNotationMask)(scope);

			if (isNaN(decimals)) {
				decimals = defaultPrecision;
			}

			var significandMask = significandMaskBuilder(decimals);

			function splitNumber(value) {
				var stringValue = value.toString(),
					splittedNumber = stringValue.match(/(-?[0-9]*)[\.]?([0-9]*)?[Ee]?([\+-]?[0-9]*)?/);

				return {
					integerPartOfSignificand: splittedNumber[1],
					decimalPartOfSignificand: splittedNumber[2],
					exponent: splittedNumber[3] | 0
				};
			}

			function formatter(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				if (typeof value === 'string') {
					value = value.replace(decimalDelimiter, '.');
				} else if (typeof value === 'number') {
					value = value.toExponential(decimals);
				}

				var formattedValue, exponent;
				var splittedNumber = splitNumber(value);

				var integerPartOfSignificand = splittedNumber.integerPartOfSignificand || 0;
				var numberToFormat = integerPartOfSignificand.toString();
				if (angular.isDefined(splittedNumber.decimalPartOfSignificand)) {
					numberToFormat += splittedNumber.decimalPartOfSignificand;
				}

				var needsNormalization =
					(integerPartOfSignificand >= 1 || integerPartOfSignificand <= -1) &&
					(
						(angular.isDefined(splittedNumber.decimalPartOfSignificand) &&
						splittedNumber.decimalPartOfSignificand.length > decimals) ||
						(decimals === 0 && numberToFormat.length >= 2)
					);

				if (needsNormalization) {
					exponent = numberToFormat.slice(decimals + 1, numberToFormat.length);
					numberToFormat = numberToFormat.slice(0, decimals + 1);
				}

				formattedValue = significandMask.apply(numberToFormat);

				if (splittedNumber.exponent !== 0) {
					exponent = splittedNumber.exponent;
				}

				if (angular.isDefined(exponent)) {
					formattedValue += 'e' + exponent;
				}

				return formattedValue;
			}

			function parser(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				var viewValue = formatter(value),
					modelValue = parseFloat(viewValue.replace(decimalDelimiter, '.'));

				if (ctrl.$viewValue !== viewValue) {
					ctrl.$setViewValue(viewValue);
					ctrl.$render();
				}

				return modelValue;
			}

			ctrl.$formatters.push(formatter);
			ctrl.$parsers.push(parser);

			ctrl.$validators.max = function validator(value) {
				return ctrl.$isEmpty(value) || value < Number.MAX_VALUE;
			};
		}
	};
}
ScientificNotationMaskDirective.$inject = ['$locale', '$parse'];

module.exports = ScientificNotationMaskDirective;

},{"string-mask":3}],24:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');

module.exports = function TimeMaskDirective() {
	return {
		restrict: 'A',
		require: 'ngModel',
		link: function(scope, element, attrs, ctrl) {
			var timeFormat = '00:00:00';

			if (angular.isDefined(attrs.uiTimeMask) && attrs.uiTimeMask === 'short') {
				timeFormat = '00:00';
			}

			var formattedValueLength = timeFormat.length;
			var unformattedValueLength = timeFormat.replace(':', '').length;
			var timeMask = new StringMask(timeFormat);

			function formatter(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				var cleanValue = value.replace(/[^0-9]/g, '').slice(0, unformattedValueLength) || '';
				return (timeMask.apply(cleanValue) || '').replace(/[^0-9]$/, '');
			}

			ctrl.$formatters.push(formatter);

			ctrl.$parsers.push(function parser(value) {
				if (ctrl.$isEmpty(value)) {
					return value;
				}

				var viewValue = formatter(value);
				var modelValue = viewValue;

				if (ctrl.$viewValue !== viewValue) {
					ctrl.$setViewValue(viewValue);
					ctrl.$render();
				}

				return modelValue;
			});

			ctrl.$validators.time = function(modelValue) {
				if (ctrl.$isEmpty(modelValue)) {
					return true;
				}

				var splittedValue = modelValue.toString().split(/:/).filter(function(v) {
					return !!v;
				});

				var hours = parseInt(splittedValue[0]),
					minutes = parseInt(splittedValue[1]),
					seconds = parseInt(splittedValue[2] || 0);

				return modelValue.toString().length === formattedValueLength &&
					hours < 24 && minutes < 60 && seconds < 60;
			};
		}
	};
};

},{"string-mask":3}],25:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');

var m = angular.module('ui.utils.masks.helpers', []);

module.exports = m.name;

m.factory('PreFormatters', [function() {
	function clearDelimitersAndLeadingZeros(value) {
		if (value === '0') {
			return '0';
		}

		var cleanValue = value.replace(/^-/,'').replace(/^0*/, '');
		return cleanValue.replace(/[^0-9]/g, '');
	}

	function prepareNumberToFormatter(value, decimals) {
		return clearDelimitersAndLeadingZeros((parseFloat(value)).toFixed(decimals));
	}

	return {
		clearDelimitersAndLeadingZeros: clearDelimitersAndLeadingZeros,
		prepareNumberToFormatter: prepareNumberToFormatter
	};
}])
.factory('NumberMasks', [function() {
	return {
		viewMask: function(decimals, decimalDelimiter, thousandsDelimiter) {
			var mask = '#' + thousandsDelimiter + '##0';

			if (decimals > 0) {
				mask += decimalDelimiter;
				for (var i = 0; i < decimals; i++) {
					mask += '0';
				}
			}

			return new StringMask(mask, {
				reverse: true
			});
		},
		modelMask: function(decimals) {
			var mask = '###0';

			if (decimals > 0) {
				mask += '.';
				for (var i = 0; i < decimals; i++) {
					mask += '0';
				}
			}

			return new StringMask(mask, {
				reverse: true
			});
		}
	};
}]);

},{"string-mask":3}],26:[function(require,module,exports){
'use strict';

var StringMask = require('string-mask');
var maskFactory = require('mask-factory');

var phoneMaskUS = new StringMask('(000) 000-0000'),
	phoneMaskINTL = new StringMask('+00-00-000-000000');

module.exports = maskFactory({
	clearValue: function(rawValue) {
		return rawValue.toString().replace(/[^0-9]/g, '');
	},
	format: function(cleanValue) {
		var formattedValue;

		if (cleanValue.length < 11) {
			formattedValue = phoneMaskUS.apply(cleanValue) || '';
		} else {
			formattedValue = phoneMaskINTL.apply(cleanValue);
		}

		return formattedValue.trim().replace(/[^0-9]$/, '');
	},
	validations: {
		usPhoneNumber: function(value) {
			return value && value.toString().length > 9;
		}
	}
});

},{"mask-factory":"mask-factory","string-mask":3}],27:[function(require,module,exports){
'use strict';

var m = angular.module('ui.utils.masks.us', [
	require('../helpers')
])
.directive('uiUsPhoneNumber', require('./phone/us-phone'));

module.exports = m.name;

},{"../helpers":25,"./phone/us-phone":26}],"mask-factory":[function(require,module,exports){
'use strict';

module.exports = function maskFactory(maskDefinition) {
	return function MaskDirective() {
		return {
			restrict: 'A',
			require: 'ngModel',
			link: function(scope, element, attrs, ctrl) {
				ctrl.$formatters.push(function formatter(value) {
					if (ctrl.$isEmpty(value)) {
						return value;
					}

					var cleanValue = maskDefinition.clearValue(value);
					return maskDefinition.format(cleanValue);
				});

				ctrl.$parsers.push(function parser(value) {
					if (ctrl.$isEmpty(value)) {
						return value;
					}

					var cleanValue = maskDefinition.clearValue(value);
					var formattedValue = maskDefinition.format(cleanValue);

					if (ctrl.$viewValue !== formattedValue) {
						ctrl.$setViewValue(formattedValue);
						ctrl.$render();
					}

					if (angular.isUndefined(maskDefinition.getModelValue)) {
						return cleanValue;
					}

					var actualModelType = typeof ctrl.$modelValue;
					return maskDefinition.getModelValue(formattedValue, actualModelType);
				});

				angular.forEach(maskDefinition.validations, function(validatorFn, validationErrorKey) {
					ctrl.$validators[validationErrorKey] = function validator(modelValue, viewValue) {
						return ctrl.$isEmpty(modelValue) || validatorFn(modelValue, viewValue);
					};
				});
			}
		};
	};
};

},{}],"validators":[function(require,module,exports){
'use strict';

module.exports = {
	maxNumber: function(ctrl, value, limit) {
		var max = parseFloat(limit, 10);
		return ctrl.$isEmpty(value) || isNaN(max) || value <= max;
	},
	minNumber: function(ctrl, value, limit) {
		var min = parseFloat(limit, 10);
		return ctrl.$isEmpty(value) || isNaN(min) || value >= min;
	}
};

},{}]},{},[4]);

/**
 * @license AngularJS v1.6.3
 * (c) 2010-2017 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/**
 * @ngdoc module
 * @name ngCookies
 * @description
 *
 * # ngCookies
 *
 * The `ngCookies` module provides a convenient wrapper for reading and writing browser cookies.
 *
 *
 * <div doc-module-components="ngCookies"></div>
 *
 * See {@link ngCookies.$cookies `$cookies`} for usage.
 */


angular.module('ngCookies', ['ng']).
  info({ angularVersion: '1.6.3' }).
  /**
   * @ngdoc provider
   * @name $cookiesProvider
   * @description
   * Use `$cookiesProvider` to change the default behavior of the {@link ngCookies.$cookies $cookies} service.
   * */
   provider('$cookies', [/** @this */function $CookiesProvider() {
    /**
     * @ngdoc property
     * @name $cookiesProvider#defaults
     * @description
     *
     * Object containing default options to pass when setting cookies.
     *
     * The object may have following properties:
     *
     * - **path** - `{string}` - The cookie will be available only for this path and its
     *   sub-paths. By default, this is the URL that appears in your `<base>` tag.
     * - **domain** - `{string}` - The cookie will be available only for this domain and
     *   its sub-domains. For security reasons the user agent will not accept the cookie
     *   if the current domain is not a sub-domain of this domain or equal to it.
     * - **expires** - `{string|Date}` - String of the form "Wdy, DD Mon YYYY HH:MM:SS GMT"
     *   or a Date object indicating the exact date/time this cookie will expire.
     * - **secure** - `{boolean}` - If `true`, then the cookie will only be available through a
     *   secured connection.
     *
     * Note: By default, the address that appears in your `<base>` tag will be used as the path.
     * This is important so that cookies will be visible for all routes when html5mode is enabled.
     *
     * @example
     *
     * ```js
     * angular.module('cookiesProviderExample', ['ngCookies'])
     *   .config(['$cookiesProvider', function($cookiesProvider) {
     *     // Setting default options
     *     $cookiesProvider.defaults.domain = 'foo.com';
     *     $cookiesProvider.defaults.secure = true;
     *   }]);
     * ```
     **/
    var defaults = this.defaults = {};

    function calcOptions(options) {
      return options ? angular.extend({}, defaults, options) : defaults;
    }

    /**
     * @ngdoc service
     * @name $cookies
     *
     * @description
     * Provides read/write access to browser's cookies.
     *
     * <div class="alert alert-info">
     * Up until Angular 1.3, `$cookies` exposed properties that represented the
     * current browser cookie values. In version 1.4, this behavior has changed, and
     * `$cookies` now provides a standard api of getters, setters etc.
     * </div>
     *
     * Requires the {@link ngCookies `ngCookies`} module to be installed.
     *
     * @example
     *
     * ```js
     * angular.module('cookiesExample', ['ngCookies'])
     *   .controller('ExampleController', ['$cookies', function($cookies) {
     *     // Retrieving a cookie
     *     var favoriteCookie = $cookies.get('myFavorite');
     *     // Setting a cookie
     *     $cookies.put('myFavorite', 'oatmeal');
     *   }]);
     * ```
     */
    this.$get = ['$$cookieReader', '$$cookieWriter', function($$cookieReader, $$cookieWriter) {
      return {
        /**
         * @ngdoc method
         * @name $cookies#get
         *
         * @description
         * Returns the value of given cookie key
         *
         * @param {string} key Id to use for lookup.
         * @returns {string} Raw cookie value.
         */
        get: function(key) {
          return $$cookieReader()[key];
        },

        /**
         * @ngdoc method
         * @name $cookies#getObject
         *
         * @description
         * Returns the deserialized value of given cookie key
         *
         * @param {string} key Id to use for lookup.
         * @returns {Object} Deserialized cookie value.
         */
        getObject: function(key) {
          var value = this.get(key);
          return value ? angular.fromJson(value) : value;
        },

        /**
         * @ngdoc method
         * @name $cookies#getAll
         *
         * @description
         * Returns a key value object with all the cookies
         *
         * @returns {Object} All cookies
         */
        getAll: function() {
          return $$cookieReader();
        },

        /**
         * @ngdoc method
         * @name $cookies#put
         *
         * @description
         * Sets a value for given cookie key
         *
         * @param {string} key Id for the `value`.
         * @param {string} value Raw value to be stored.
         * @param {Object=} options Options object.
         *    See {@link ngCookies.$cookiesProvider#defaults $cookiesProvider.defaults}
         */
        put: function(key, value, options) {
          $$cookieWriter(key, value, calcOptions(options));
        },

        /**
         * @ngdoc method
         * @name $cookies#putObject
         *
         * @description
         * Serializes and sets a value for given cookie key
         *
         * @param {string} key Id for the `value`.
         * @param {Object} value Value to be stored.
         * @param {Object=} options Options object.
         *    See {@link ngCookies.$cookiesProvider#defaults $cookiesProvider.defaults}
         */
        putObject: function(key, value, options) {
          this.put(key, angular.toJson(value), options);
        },

        /**
         * @ngdoc method
         * @name $cookies#remove
         *
         * @description
         * Remove given cookie
         *
         * @param {string} key Id of the key-value pair to delete.
         * @param {Object=} options Options object.
         *    See {@link ngCookies.$cookiesProvider#defaults $cookiesProvider.defaults}
         */
        remove: function(key, options) {
          $$cookieWriter(key, undefined, calcOptions(options));
        }
      };
    }];
  }]);

angular.module('ngCookies').
/**
 * @ngdoc service
 * @name $cookieStore
 * @deprecated
 * sinceVersion="v1.4.0"
 * Please use the {@link ngCookies.$cookies `$cookies`} service instead.
 *
 * @requires $cookies
 *
 * @description
 * Provides a key-value (string-object) storage, that is backed by session cookies.
 * Objects put or retrieved from this storage are automatically serialized or
 * deserialized by angular's toJson/fromJson.
 *
 * Requires the {@link ngCookies `ngCookies`} module to be installed.
 *
 * @example
 *
 * ```js
 * angular.module('cookieStoreExample', ['ngCookies'])
 *   .controller('ExampleController', ['$cookieStore', function($cookieStore) {
 *     // Put cookie
 *     $cookieStore.put('myFavorite','oatmeal');
 *     // Get cookie
 *     var favoriteCookie = $cookieStore.get('myFavorite');
 *     // Removing a cookie
 *     $cookieStore.remove('myFavorite');
 *   }]);
 * ```
 */
 factory('$cookieStore', ['$cookies', function($cookies) {

    return {
      /**
       * @ngdoc method
       * @name $cookieStore#get
       *
       * @description
       * Returns the value of given cookie key
       *
       * @param {string} key Id to use for lookup.
       * @returns {Object} Deserialized cookie value, undefined if the cookie does not exist.
       */
      get: function(key) {
        return $cookies.getObject(key);
      },

      /**
       * @ngdoc method
       * @name $cookieStore#put
       *
       * @description
       * Sets a value for given cookie key
       *
       * @param {string} key Id for the `value`.
       * @param {Object} value Value to be stored.
       */
      put: function(key, value) {
        $cookies.putObject(key, value);
      },

      /**
       * @ngdoc method
       * @name $cookieStore#remove
       *
       * @description
       * Remove given cookie
       *
       * @param {string} key Id of the key-value pair to delete.
       */
      remove: function(key) {
        $cookies.remove(key);
      }
    };

  }]);

/**
 * @name $$cookieWriter
 * @requires $document
 *
 * @description
 * This is a private service for writing cookies
 *
 * @param {string} name Cookie name
 * @param {string=} value Cookie value (if undefined, cookie will be deleted)
 * @param {Object=} options Object with options that need to be stored for the cookie.
 */
function $$CookieWriter($document, $log, $browser) {
  var cookiePath = $browser.baseHref();
  var rawDocument = $document[0];

  function buildCookieString(name, value, options) {
    var path, expires;
    options = options || {};
    expires = options.expires;
    path = angular.isDefined(options.path) ? options.path : cookiePath;
    if (angular.isUndefined(value)) {
      expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
      value = '';
    }
    if (angular.isString(expires)) {
      expires = new Date(expires);
    }

    var str = encodeURIComponent(name) + '=' + encodeURIComponent(value);
    str += path ? ';path=' + path : '';
    str += options.domain ? ';domain=' + options.domain : '';
    str += expires ? ';expires=' + expires.toUTCString() : '';
    str += options.secure ? ';secure' : '';

    // per http://www.ietf.org/rfc/rfc2109.txt browser must allow at minimum:
    // - 300 cookies
    // - 20 cookies per unique domain
    // - 4096 bytes per cookie
    var cookieLength = str.length + 1;
    if (cookieLength > 4096) {
      $log.warn('Cookie \'' + name +
        '\' possibly not set or overflowed because it was too large (' +
        cookieLength + ' > 4096 bytes)!');
    }

    return str;
  }

  return function(name, value, options) {
    rawDocument.cookie = buildCookieString(name, value, options);
  };
}

$$CookieWriter.$inject = ['$document', '$log', '$browser'];

angular.module('ngCookies').provider('$$cookieWriter', /** @this */ function $$CookieWriterProvider() {
  this.$get = $$CookieWriter;
});


})(window, window.angular);

/**
 * An Angular module that gives you access to the browsers local storage
 * @version v0.2.3 - 2015-10-11
 * @link https://github.com/grevory/angular-local-storage
 * @author grevory <greg@gregpike.ca>
 * @license MIT License, http://www.opensource.org/licenses/MIT
 */
(function ( window, angular, undefined ) {
/*jshint globalstrict:true*/
'use strict';

var isDefined = angular.isDefined,
  isUndefined = angular.isUndefined,
  isNumber = angular.isNumber,
  isObject = angular.isObject,
  isArray = angular.isArray,
  extend = angular.extend,
  toJson = angular.toJson;
var angularLocalStorage = angular.module('LocalStorageModule', []);

angularLocalStorage.provider('localStorageService', function() {

  // You should set a prefix to avoid overwriting any local storage variables from the rest of your app
  // e.g. localStorageServiceProvider.setPrefix('yourAppName');
  // With provider you can use config as this:
  // myApp.config(function (localStorageServiceProvider) {
  //    localStorageServiceProvider.prefix = 'yourAppName';
  // });
  this.prefix = 'ls';

  // You could change web storage type localstorage or sessionStorage
  this.storageType = 'localStorage';

  // Cookie options (usually in case of fallback)
  // expiry = Number of days before cookies expire // 0 = Does not expire
  // path = The web path the cookie represents
  this.cookie = {
    expiry: 30,
    path: '/'
  };

  // Send signals for each of the following actions?
  this.notify = {
    setItem: true,
    removeItem: false
  };

  // Setter for the prefix
  this.setPrefix = function(prefix) {
    this.prefix = prefix;
    return this;
  };

   // Setter for the storageType
   this.setStorageType = function(storageType) {
     this.storageType = storageType;
     return this;
   };

  // Setter for cookie config
  this.setStorageCookie = function(exp, path) {
    this.cookie.expiry = exp;
    this.cookie.path = path;
    return this;
  };

  // Setter for cookie domain
  this.setStorageCookieDomain = function(domain) {
    this.cookie.domain = domain;
    return this;
  };

  // Setter for notification config
  // itemSet & itemRemove should be booleans
  this.setNotify = function(itemSet, itemRemove) {
    this.notify = {
      setItem: itemSet,
      removeItem: itemRemove
    };
    return this;
  };

  this.$get = ['$rootScope', '$window', '$document', '$parse', function($rootScope, $window, $document, $parse) {
    var self = this;
    var prefix = self.prefix;
    var cookie = self.cookie;
    var notify = self.notify;
    var storageType = self.storageType;
    var webStorage;

    // When Angular's $document is not available
    if (!$document) {
      $document = document;
    } else if ($document[0]) {
      $document = $document[0];
    }

    // If there is a prefix set in the config lets use that with an appended period for readability
    if (prefix.substr(-1) !== '.') {
      prefix = !!prefix ? prefix + '.' : '';
    }
    var deriveQualifiedKey = function(key) {
      return prefix + key;
    };
    // Checks the browser to see if local storage is supported
    var browserSupportsLocalStorage = (function () {
      try {
        var supported = (storageType in $window && $window[storageType] !== null);

        // When Safari (OS X or iOS) is in private browsing mode, it appears as though localStorage
        // is available, but trying to call .setItem throws an exception.
        //
        // "QUOTA_EXCEEDED_ERR: DOM Exception 22: An attempt was made to add something to storage
        // that exceeded the quota."
        var key = deriveQualifiedKey('__' + Math.round(Math.random() * 1e7));
        if (supported) {
          webStorage = $window[storageType];
          webStorage.setItem(key, '');
          webStorage.removeItem(key);
        }

        return supported;
      } catch (e) {
        storageType = 'cookie';
        $rootScope.$broadcast('LocalStorageModule.notification.error', e.message);
        return false;
      }
    }());

    // Directly adds a value to local storage
    // If local storage is not available in the browser use cookies
    // Example use: localStorageService.add('library','angular');
    var addToLocalStorage = function (key, value) {
      // Let's convert undefined values to null to get the value consistent
      if (isUndefined(value)) {
        value = null;
      } else {
        value = toJson(value);
      }

      // If this browser does not support local storage use cookies
      if (!browserSupportsLocalStorage || self.storageType === 'cookie') {
        if (!browserSupportsLocalStorage) {
            $rootScope.$broadcast('LocalStorageModule.notification.warning', 'LOCAL_STORAGE_NOT_SUPPORTED');
        }

        if (notify.setItem) {
          $rootScope.$broadcast('LocalStorageModule.notification.setitem', {key: key, newvalue: value, storageType: 'cookie'});
        }
        return addToCookies(key, value);
      }

      try {
        if (webStorage) {
          webStorage.setItem(deriveQualifiedKey(key), value);
        }
        if (notify.setItem) {
          $rootScope.$broadcast('LocalStorageModule.notification.setitem', {key: key, newvalue: value, storageType: self.storageType});
        }
      } catch (e) {
        $rootScope.$broadcast('LocalStorageModule.notification.error', e.message);
        return addToCookies(key, value);
      }
      return true;
    };

    // Directly get a value from local storage
    // Example use: localStorageService.get('library'); // returns 'angular'
    var getFromLocalStorage = function (key) {

      if (!browserSupportsLocalStorage || self.storageType === 'cookie') {
        if (!browserSupportsLocalStorage) {
          $rootScope.$broadcast('LocalStorageModule.notification.warning','LOCAL_STORAGE_NOT_SUPPORTED');
        }

        return getFromCookies(key);
      }

      var item = webStorage ? webStorage.getItem(deriveQualifiedKey(key)) : null;
      // angular.toJson will convert null to 'null', so a proper conversion is needed
      // FIXME not a perfect solution, since a valid 'null' string can't be stored
      if (!item || item === 'null') {
        return null;
      }

      try {
        return JSON.parse(item);
      } catch (e) {
        return item;
      }
    };

    // Remove an item from local storage
    // Example use: localStorageService.remove('library'); // removes the key/value pair of library='angular'
    var removeFromLocalStorage = function () {
      var i, key;
      for (i=0; i<arguments.length; i++) {
        key = arguments[i];
        if (!browserSupportsLocalStorage || self.storageType === 'cookie') {
          if (!browserSupportsLocalStorage) {
            $rootScope.$broadcast('LocalStorageModule.notification.warning', 'LOCAL_STORAGE_NOT_SUPPORTED');
          }

          if (notify.removeItem) {
            $rootScope.$broadcast('LocalStorageModule.notification.removeitem', {key: key, storageType: 'cookie'});
          }
          removeFromCookies(key);
        }
        else {
          try {
            webStorage.removeItem(deriveQualifiedKey(key));
            if (notify.removeItem) {
              $rootScope.$broadcast('LocalStorageModule.notification.removeitem', {
                key: key,
                storageType: self.storageType
              });
            }
          } catch (e) {
            $rootScope.$broadcast('LocalStorageModule.notification.error', e.message);
            removeFromCookies(key);
          }
        }
      }
    };

    // Return array of keys for local storage
    // Example use: var keys = localStorageService.keys()
    var getKeysForLocalStorage = function () {

      if (!browserSupportsLocalStorage) {
        $rootScope.$broadcast('LocalStorageModule.notification.warning', 'LOCAL_STORAGE_NOT_SUPPORTED');
        return false;
      }

      var prefixLength = prefix.length;
      var keys = [];
      for (var key in webStorage) {
        // Only return keys that are for this app
        if (key.substr(0,prefixLength) === prefix) {
          try {
            keys.push(key.substr(prefixLength));
          } catch (e) {
            $rootScope.$broadcast('LocalStorageModule.notification.error', e.Description);
            return [];
          }
        }
      }
      return keys;
    };

    // Remove all data for this app from local storage
    // Also optionally takes a regular expression string and removes the matching key-value pairs
    // Example use: localStorageService.clearAll();
    // Should be used mostly for development purposes
    var clearAllFromLocalStorage = function (regularExpression) {

      // Setting both regular expressions independently
      // Empty strings result in catchall RegExp
      var prefixRegex = !!prefix ? new RegExp('^' + prefix) : new RegExp();
      var testRegex = !!regularExpression ? new RegExp(regularExpression) : new RegExp();

      if (!browserSupportsLocalStorage || self.storageType === 'cookie') {
        if (!browserSupportsLocalStorage) {
          $rootScope.$broadcast('LocalStorageModule.notification.warning', 'LOCAL_STORAGE_NOT_SUPPORTED');
        }
        return clearAllFromCookies();
      }

      var prefixLength = prefix.length;

      for (var key in webStorage) {
        // Only remove items that are for this app and match the regular expression
        if (prefixRegex.test(key) && testRegex.test(key.substr(prefixLength))) {
          try {
            removeFromLocalStorage(key.substr(prefixLength));
          } catch (e) {
            $rootScope.$broadcast('LocalStorageModule.notification.error',e.message);
            return clearAllFromCookies();
          }
        }
      }
      return true;
    };

    // Checks the browser to see if cookies are supported
    var browserSupportsCookies = (function() {
      try {
        return $window.navigator.cookieEnabled ||
          ("cookie" in $document && ($document.cookie.length > 0 ||
          ($document.cookie = "test").indexOf.call($document.cookie, "test") > -1));
      } catch (e) {
          $rootScope.$broadcast('LocalStorageModule.notification.error', e.message);
          return false;
      }
    }());

    // Directly adds a value to cookies
    // Typically used as a fallback is local storage is not available in the browser
    // Example use: localStorageService.cookie.add('library','angular');
    var addToCookies = function (key, value, daysToExpiry) {

      if (isUndefined(value)) {
        return false;
      } else if(isArray(value) || isObject(value)) {
        value = toJson(value);
      }

      if (!browserSupportsCookies) {
        $rootScope.$broadcast('LocalStorageModule.notification.error', 'COOKIES_NOT_SUPPORTED');
        return false;
      }

      try {
        var expiry = '',
            expiryDate = new Date(),
            cookieDomain = '';

        if (value === null) {
          // Mark that the cookie has expired one day ago
          expiryDate.setTime(expiryDate.getTime() + (-1 * 24 * 60 * 60 * 1000));
          expiry = "; expires=" + expiryDate.toGMTString();
          value = '';
        } else if (isNumber(daysToExpiry) && daysToExpiry !== 0) {
          expiryDate.setTime(expiryDate.getTime() + (daysToExpiry * 24 * 60 * 60 * 1000));
          expiry = "; expires=" + expiryDate.toGMTString();
        } else if (cookie.expiry !== 0) {
          expiryDate.setTime(expiryDate.getTime() + (cookie.expiry * 24 * 60 * 60 * 1000));
          expiry = "; expires=" + expiryDate.toGMTString();
        }
        if (!!key) {
          var cookiePath = "; path=" + cookie.path;
          if(cookie.domain){
            cookieDomain = "; domain=" + cookie.domain;
          }
          $document.cookie = deriveQualifiedKey(key) + "=" + encodeURIComponent(value) + expiry + cookiePath + cookieDomain;
        }
      } catch (e) {
        $rootScope.$broadcast('LocalStorageModule.notification.error',e.message);
        return false;
      }
      return true;
    };

    // Directly get a value from a cookie
    // Example use: localStorageService.cookie.get('library'); // returns 'angular'
    var getFromCookies = function (key) {
      if (!browserSupportsCookies) {
        $rootScope.$broadcast('LocalStorageModule.notification.error', 'COOKIES_NOT_SUPPORTED');
        return false;
      }

      var cookies = $document.cookie && $document.cookie.split(';') || [];
      for(var i=0; i < cookies.length; i++) {
        var thisCookie = cookies[i];
        while (thisCookie.charAt(0) === ' ') {
          thisCookie = thisCookie.substring(1,thisCookie.length);
        }
        if (thisCookie.indexOf(deriveQualifiedKey(key) + '=') === 0) {
          var storedValues = decodeURIComponent(thisCookie.substring(prefix.length + key.length + 1, thisCookie.length));
          try {
            return JSON.parse(storedValues);
          } catch(e) {
            return storedValues;
          }
        }
      }
      return null;
    };

    var removeFromCookies = function (key) {
      addToCookies(key,null);
    };

    var clearAllFromCookies = function () {
      var thisCookie = null, thisKey = null;
      var prefixLength = prefix.length;
      var cookies = $document.cookie.split(';');
      for(var i = 0; i < cookies.length; i++) {
        thisCookie = cookies[i];

        while (thisCookie.charAt(0) === ' ') {
          thisCookie = thisCookie.substring(1, thisCookie.length);
        }

        var key = thisCookie.substring(prefixLength, thisCookie.indexOf('='));
        removeFromCookies(key);
      }
    };

    var getStorageType = function() {
      return storageType;
    };

    // Add a listener on scope variable to save its changes to local storage
    // Return a function which when called cancels binding
    var bindToScope = function(scope, key, def, lsKey) {
      lsKey = lsKey || key;
      var value = getFromLocalStorage(lsKey);

      if (value === null && isDefined(def)) {
        value = def;
      } else if (isObject(value) && isObject(def)) {
        value = extend(def, value);
      }

      $parse(key).assign(scope, value);

      return scope.$watch(key, function(newVal) {
        addToLocalStorage(lsKey, newVal);
      }, isObject(scope[key]));
    };

    // Return localStorageService.length
    // ignore keys that not owned
    var lengthOfLocalStorage = function() {
      var count = 0;
      var storage = $window[storageType];
      for(var i = 0; i < storage.length; i++) {
        if(storage.key(i).indexOf(prefix) === 0 ) {
          count++;
        }
      }
      return count;
    };

    return {
      isSupported: browserSupportsLocalStorage,
      getStorageType: getStorageType,
      set: addToLocalStorage,
      add: addToLocalStorage, //DEPRECATED
      get: getFromLocalStorage,
      keys: getKeysForLocalStorage,
      remove: removeFromLocalStorage,
      clearAll: clearAllFromLocalStorage,
      bind: bindToScope,
      deriveKey: deriveQualifiedKey,
      length: lengthOfLocalStorage,
      cookie: {
        isSupported: browserSupportsCookies,
        set: addToCookies,
        add: addToCookies, //DEPRECATED
        get: getFromCookies,
        remove: removeFromCookies,
        clearAll: clearAllFromCookies
      }
    };
  }];
});
})( window, window.angular );
/*! 
 * angular-loading-bar v0.8.0
 * https://chieffancypants.github.io/angular-loading-bar
 * Copyright (c) 2015 Wes Cruver
 * License: MIT
 */
/*
 * angular-loading-bar
 *
 * intercepts XHR requests and creates a loading bar.
 * Based on the excellent nprogress work by rstacruz (more info in readme)
 *
 * (c) 2013 Wes Cruver
 * License: MIT
 */


(function() {

'use strict';

// Alias the loading bar for various backwards compatibilities since the project has matured:
angular.module('angular-loading-bar', ['cfp.loadingBarInterceptor']);
angular.module('chieffancypants.loadingBar', ['cfp.loadingBarInterceptor']);


/**
 * loadingBarInterceptor service
 *
 * Registers itself as an Angular interceptor and listens for XHR requests.
 */
angular.module('cfp.loadingBarInterceptor', ['cfp.loadingBar'])
  .config(['$httpProvider', function ($httpProvider) {

    var interceptor = ['$q', '$cacheFactory', '$timeout', '$rootScope', '$log', 'cfpLoadingBar', function ($q, $cacheFactory, $timeout, $rootScope, $log, cfpLoadingBar) {

      /**
       * The total number of requests made
       */
      var reqsTotal = 0;

      /**
       * The number of requests completed (either successfully or not)
       */
      var reqsCompleted = 0;

      /**
       * The amount of time spent fetching before showing the loading bar
       */
      var latencyThreshold = cfpLoadingBar.latencyThreshold;

      /**
       * $timeout handle for latencyThreshold
       */
      var startTimeout;


      /**
       * calls cfpLoadingBar.complete() which removes the
       * loading bar from the DOM.
       */
      function setComplete() {
        $timeout.cancel(startTimeout);
        cfpLoadingBar.complete();
        reqsCompleted = 0;
        reqsTotal = 0;
      }

      /**
       * Determine if the response has already been cached
       * @param  {Object}  config the config option from the request
       * @return {Boolean} retrns true if cached, otherwise false
       */
      function isCached(config) {
        var cache;
        var defaultCache = $cacheFactory.get('$http');
        var defaults = $httpProvider.defaults;

        // Choose the proper cache source. Borrowed from angular: $http service
        if ((config.cache || defaults.cache) && config.cache !== false &&
          (config.method === 'GET' || config.method === 'JSONP')) {
            cache = angular.isObject(config.cache) ? config.cache
              : angular.isObject(defaults.cache) ? defaults.cache
              : defaultCache;
        }

        var cached = cache !== undefined ?
          cache.get(config.url) !== undefined : false;

        if (config.cached !== undefined && cached !== config.cached) {
          return config.cached;
        }
        config.cached = cached;
        return cached;
      }


      return {
        'request': function(config) {
          // Check to make sure this request hasn't already been cached and that
          // the requester didn't explicitly ask us to ignore this request:
          if (!config.ignoreLoadingBar && !isCached(config)) {
            $rootScope.$broadcast('cfpLoadingBar:loading', {url: config.url});
            if (reqsTotal === 0) {
              startTimeout = $timeout(function() {
                cfpLoadingBar.start();
              }, latencyThreshold);
            }
            reqsTotal++;
            cfpLoadingBar.set(reqsCompleted / reqsTotal);
          }
          return config;
        },

        'response': function(response) {
          if (!response || !response.config) {
            $log.error('Broken interceptor detected: Config object not supplied in response:\n https://github.com/chieffancypants/angular-loading-bar/pull/50');
            return response;
          }

          if (!response.config.ignoreLoadingBar && !isCached(response.config)) {
            reqsCompleted++;
            $rootScope.$broadcast('cfpLoadingBar:loaded', {url: response.config.url, result: response});
            if (reqsCompleted >= reqsTotal) {
              setComplete();
            } else {
              cfpLoadingBar.set(reqsCompleted / reqsTotal);
            }
          }
          return response;
        },

        'responseError': function(rejection) {
          if (!rejection || !rejection.config) {
            $log.error('Broken interceptor detected: Config object not supplied in rejection:\n https://github.com/chieffancypants/angular-loading-bar/pull/50');
            return $q.reject(rejection);
          }

          if (!rejection.config.ignoreLoadingBar && !isCached(rejection.config)) {
            reqsCompleted++;
            $rootScope.$broadcast('cfpLoadingBar:loaded', {url: rejection.config.url, result: rejection});
            if (reqsCompleted >= reqsTotal) {
              setComplete();
            } else {
              cfpLoadingBar.set(reqsCompleted / reqsTotal);
            }
          }
          return $q.reject(rejection);
        }
      };
    }];

    $httpProvider.interceptors.push(interceptor);
  }]);


/**
 * Loading Bar
 *
 * This service handles adding and removing the actual element in the DOM.
 * Generally, best practices for DOM manipulation is to take place in a
 * directive, but because the element itself is injected in the DOM only upon
 * XHR requests, and it's likely needed on every view, the best option is to
 * use a service.
 */
angular.module('cfp.loadingBar', [])
  .provider('cfpLoadingBar', function() {

    this.autoIncrement = true;
    this.includeSpinner = true;
    this.includeBar = true;
    this.latencyThreshold = 100;
    this.startSize = 0.02;
    this.parentSelector = 'body';
    this.spinnerTemplate = '<div id="loading-bar-spinner"><div class="spinner-icon"></div></div>';
    this.loadingBarTemplate = '<div id="loading-bar"><div class="bar"><div class="peg"></div></div></div>';

    this.$get = ['$injector', '$document', '$timeout', '$rootScope', function ($injector, $document, $timeout, $rootScope) {
      var $animate;
      var $parentSelector = this.parentSelector,
        loadingBarContainer = angular.element(this.loadingBarTemplate),
        loadingBar = loadingBarContainer.find('div').eq(0),
        spinner = angular.element(this.spinnerTemplate);

      var incTimeout,
        completeTimeout,
        started = false,
        status = 0;

      var autoIncrement = this.autoIncrement;
      var includeSpinner = this.includeSpinner;
      var includeBar = this.includeBar;
      var startSize = this.startSize;

      /**
       * Inserts the loading bar element into the dom, and sets it to 2%
       */
      function _start() {
        if (!$animate) {
          $animate = $injector.get('$animate');
        }

        var $parent = $document.find($parentSelector).eq(0);
        $timeout.cancel(completeTimeout);

        // do not continually broadcast the started event:
        if (started) {
          return;
        }

        $rootScope.$broadcast('cfpLoadingBar:started');
        started = true;

        if (includeBar) {
          $animate.enter(loadingBarContainer, $parent, angular.element($parent[0].lastChild));
        }

        if (includeSpinner) {
          $animate.enter(spinner, $parent, angular.element($parent[0].lastChild));
        }

        _set(startSize);
      }

      /**
       * Set the loading bar's width to a certain percent.
       *
       * @param n any value between 0 and 1
       */
      function _set(n) {
        if (!started) {
          return;
        }
        var pct = (n * 100) + '%';
        loadingBar.css('width', pct);
        status = n;

        // increment loadingbar to give the illusion that there is always
        // progress but make sure to cancel the previous timeouts so we don't
        // have multiple incs running at the same time.
        if (autoIncrement) {
          $timeout.cancel(incTimeout);
          incTimeout = $timeout(function() {
            _inc();
          }, 250);
        }
      }

      /**
       * Increments the loading bar by a random amount
       * but slows down as it progresses
       */
      function _inc() {
        if (_status() >= 1) {
          return;
        }

        var rnd = 0;

        // TODO: do this mathmatically instead of through conditions

        var stat = _status();
        if (stat >= 0 && stat < 0.25) {
          // Start out between 3 - 6% increments
          rnd = (Math.random() * (5 - 3 + 1) + 3) / 100;
        } else if (stat >= 0.25 && stat < 0.65) {
          // increment between 0 - 3%
          rnd = (Math.random() * 3) / 100;
        } else if (stat >= 0.65 && stat < 0.9) {
          // increment between 0 - 2%
          rnd = (Math.random() * 2) / 100;
        } else if (stat >= 0.9 && stat < 0.99) {
          // finally, increment it .5 %
          rnd = 0.005;
        } else {
          // after 99%, don't increment:
          rnd = 0;
        }

        var pct = _status() + rnd;
        _set(pct);
      }

      function _status() {
        return status;
      }

      function _completeAnimation() {
        status = 0;
        started = false;
      }

      function _complete() {
        if (!$animate) {
          $animate = $injector.get('$animate');
        }

        $rootScope.$broadcast('cfpLoadingBar:completed');
        _set(1);

        $timeout.cancel(completeTimeout);

        // Attempt to aggregate any start/complete calls within 500ms:
        completeTimeout = $timeout(function() {
          var promise = $animate.leave(loadingBarContainer, _completeAnimation);
          if (promise && promise.then) {
            promise.then(_completeAnimation);
          }
          $animate.leave(spinner);
        }, 500);
      }

      return {
        start            : _start,
        set              : _set,
        status           : _status,
        inc              : _inc,
        complete         : _complete,
        autoIncrement    : this.autoIncrement,
        includeSpinner   : this.includeSpinner,
        latencyThreshold : this.latencyThreshold,
        parentSelector   : this.parentSelector,
        startSize        : this.startSize
      };


    }];     //
  });       // wtf javascript. srsly
})();       //

/**
 * @license AngularJS v1.8.3
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

var forEach;
var isArray;
var isString;
var jqLite;

/**
 * @ngdoc module
 * @name ngMessages
 * @description
 *
 * The `ngMessages` module provides enhanced support for displaying messages within templates
 * (typically within forms or when rendering message objects that return key/value data).
 * Instead of relying on JavaScript code and/or complex ng-if statements within your form template to
 * show and hide error messages specific to the state of an input field, the `ngMessages` and
 * `ngMessage` directives are designed to handle the complexity, inheritance and priority
 * sequencing based on the order of how the messages are defined in the template.
 *
 * Currently, the ngMessages module only contains the code for the `ngMessages`, `ngMessagesInclude`
 * `ngMessage`, `ngMessageExp` and `ngMessageDefault` directives.
 *
 * ## Usage
 * The `ngMessages` directive allows keys in a key/value collection to be associated with a child element
 * (or 'message') that will show or hide based on the truthiness of that key's value in the collection. A common use
 * case for `ngMessages` is to display error messages for inputs using the `$error` object exposed by the
 * {@link ngModel ngModel} directive.
 *
 * The child elements of the `ngMessages` directive are matched to the collection keys by a `ngMessage` or
 * `ngMessageExp` directive. The value of these attributes must match a key in the collection that is provided by
 * the `ngMessages` directive.
 *
 * Consider the following example, which illustrates a typical use case of `ngMessages`. Within the form `myForm` we
 * have a text input named `myField` which is bound to the scope variable `field` using the {@link ngModel ngModel}
 * directive.
 *
 * The `myField` field is a required input of type `email` with a maximum length of 15 characters.
 *
 * ```html
 * <form name="myForm">
 *   <label>
 *     Enter text:
 *     <input type="email" ng-model="field" name="myField" required maxlength="15" />
 *   </label>
 *   <div ng-messages="myForm.myField.$error" role="alert">
 *     <div ng-message="required">Please enter a value for this field.</div>
 *     <div ng-message="email">This field must be a valid email address.</div>
 *     <div ng-message="maxlength">This field can be at most 15 characters long.</div>
 *   </div>
 * </form>
 * ```
 *
 * In order to show error messages corresponding to `myField` we first create an element with an `ngMessages` attribute
 * set to the `$error` object owned by the `myField` input in our `myForm` form.
 *
 * Within this element we then create separate elements for each of the possible errors that `myField` could have.
 * The `ngMessage` attribute is used to declare which element(s) will appear for which error - for example,
 * setting `ng-message="required"` specifies that this particular element should be displayed when there
 * is no value present for the required field `myField` (because the key `required` will be `true` in the object
 * `myForm.myField.$error`).
 *
 * ### Message order
 *
 * By default, `ngMessages` will only display one message for a particular key/value collection at any time. If more
 * than one message (or error) key is currently true, then which message is shown is determined by the order of messages
 * in the HTML template code (messages declared first are prioritised). This mechanism means the developer does not have
 * to prioritize messages using custom JavaScript code.
 *
 * Given the following error object for our example (which informs us that the field `myField` currently has both the
 * `required` and `email` errors):
 *
 * ```javascript
 * <!-- keep in mind that ngModel automatically sets these error flags -->
 * myField.$error = { required : true, email: true, maxlength: false };
 * ```
 * The `required` message will be displayed to the user since it appears before the `email` message in the DOM.
 * Once the user types a single character, the `required` message will disappear (since the field now has a value)
 * but the `email` message will be visible because it is still applicable.
 *
 * ### Displaying multiple messages at the same time
 *
 * While `ngMessages` will by default only display one error element at a time, the `ng-messages-multiple` attribute can
 * be applied to the `ngMessages` container element to cause it to display all applicable error messages at once:
 *
 * ```html
 * <!-- attribute-style usage -->
 * <div ng-messages="myForm.myField.$error" ng-messages-multiple>...</div>
 *
 * <!-- element-style usage -->
 * <ng-messages for="myForm.myField.$error" multiple>...</ng-messages>
 * ```
 *
 * ## Reusing and Overriding Messages
 * In addition to prioritization, ngMessages also allows for including messages from a remote or an inline
 * template. This allows for generic collection of messages to be reused across multiple parts of an
 * application.
 *
 * ```html
 * <script type="text/ng-template" id="error-messages">
 *   <div ng-message="required">This field is required</div>
 *   <div ng-message="minlength">This field is too short</div>
 * </script>
 *
 * <div ng-messages="myForm.myField.$error" role="alert">
 *   <div ng-messages-include="error-messages"></div>
 * </div>
 * ```
 *
 * However, including generic messages may not be useful enough to match all input fields, therefore,
 * `ngMessages` provides the ability to override messages defined in the remote template by redefining
 * them within the directive container.
 *
 * ```html
 * <!-- a generic template of error messages known as "my-custom-messages" -->
 * <script type="text/ng-template" id="my-custom-messages">
 *   <div ng-message="required">This field is required</div>
 *   <div ng-message="minlength">This field is too short</div>
 * </script>
 *
 * <form name="myForm">
 *   <label>
 *     Email address
 *     <input type="email"
 *            id="email"
 *            name="myEmail"
 *            ng-model="email"
 *            minlength="5"
 *            required />
 *   </label>
 *   <!-- any ng-message elements that appear BEFORE the ng-messages-include will
 *        override the messages present in the ng-messages-include template -->
 *   <div ng-messages="myForm.myEmail.$error" role="alert">
 *     <!-- this required message has overridden the template message -->
 *     <div ng-message="required">You did not enter your email address</div>
 *
 *     <!-- this is a brand new message and will appear last in the prioritization -->
 *     <div ng-message="email">Your email address is invalid</div>
 *
 *     <!-- and here are the generic error messages -->
 *     <div ng-messages-include="my-custom-messages"></div>
 *   </div>
 * </form>
 * ```
 *
 * In the example HTML code above the message that is set on required will override the corresponding
 * required message defined within the remote template. Therefore, with particular input fields (such
 * email addresses, date fields, autocomplete inputs, etc...), specialized error messages can be applied
 * while more generic messages can be used to handle other, more general input errors.
 *
 * ## Dynamic Messaging
 * ngMessages also supports using expressions to dynamically change key values. Using arrays and
 * repeaters to list messages is also supported. This means that the code below will be able to
 * fully adapt itself and display the appropriate message when any of the expression data changes:
 *
 * ```html
 * <form name="myForm">
 *   <label>
 *     Email address
 *     <input type="email"
 *            name="myEmail"
 *            ng-model="email"
 *            minlength="5"
 *            required />
 *   </label>
 *   <div ng-messages="myForm.myEmail.$error" role="alert">
 *     <div ng-message="required">You did not enter your email address</div>
 *     <div ng-repeat="errorMessage in errorMessages">
 *       <!-- use ng-message-exp for a message whose key is given by an expression -->
 *       <div ng-message-exp="errorMessage.type">{{ errorMessage.text }}</div>
 *     </div>
 *   </div>
 * </form>
 * ```
 *
 * The `errorMessage.type` expression can be a string value or it can be an array so
 * that multiple errors can be associated with a single error message:
 *
 * ```html
 *   <label>
 *     Email address
 *     <input type="email"
 *            ng-model="data.email"
 *            name="myEmail"
 *            ng-minlength="5"
 *            ng-maxlength="100"
 *            required />
 *   </label>
 *   <div ng-messages="myForm.myEmail.$error" role="alert">
 *     <div ng-message-exp="'required'">You did not enter your email address</div>
 *     <div ng-message-exp="['minlength', 'maxlength']">
 *       Your email must be between 5 and 100 characters long
 *     </div>
 *   </div>
 * ```
 *
 * Feel free to use other structural directives such as ng-if and ng-switch to further control
 * what messages are active and when. Be careful, if you place ng-message on the same element
 * as these structural directives, AngularJS may not be able to determine if a message is active
 * or not. Therefore it is best to place the ng-message on a child element of the structural
 * directive.
 *
 * ```html
 * <div ng-messages="myForm.myEmail.$error" role="alert">
 *   <div ng-if="showRequiredError">
 *     <div ng-message="required">Please enter something</div>
 *   </div>
 * </div>
 * ```
 *
 * ## Animations
 * If the `ngAnimate` module is active within the application then the `ngMessages`, `ngMessage` and
 * `ngMessageExp` directives will trigger animations whenever any messages are added and removed from
 * the DOM by the `ngMessages` directive.
 *
 * Whenever the `ngMessages` directive contains one or more visible messages then the `.ng-active` CSS
 * class will be added to the element. The `.ng-inactive` CSS class will be applied when there are no
 * messages present. Therefore, CSS transitions and keyframes as well as JavaScript animations can
 * hook into the animations whenever these classes are added/removed.
 *
 * Let's say that our HTML code for our messages container looks like so:
 *
 * ```html
 * <div ng-messages="myMessages" class="my-messages" role="alert">
 *   <div ng-message="alert" class="some-message">...</div>
 *   <div ng-message="fail" class="some-message">...</div>
 * </div>
 * ```
 *
 * Then the CSS animation code for the message container looks like so:
 *
 * ```css
 * .my-messages {
 *   transition:1s linear all;
 * }
 * .my-messages.ng-active {
 *   // messages are visible
 * }
 * .my-messages.ng-inactive {
 *   // messages are hidden
 * }
 * ```
 *
 * Whenever an inner message is attached (becomes visible) or removed (becomes hidden) then the enter
 * and leave animation is triggered for each particular element bound to the `ngMessage` directive.
 *
 * Therefore, the CSS code for the inner messages looks like so:
 *
 * ```css
 * .some-message {
 *   transition:1s linear all;
 * }
 *
 * .some-message.ng-enter {}
 * .some-message.ng-enter.ng-enter-active {}
 *
 * .some-message.ng-leave {}
 * .some-message.ng-leave.ng-leave-active {}
 * ```
 *
 * {@link ngAnimate See the ngAnimate docs} to learn how to use JavaScript animations or to learn
 * more about ngAnimate.
 *
 * ## Displaying a default message
 * If the ngMessages renders no inner ngMessage directive (i.e. when none of the truthy
 * keys are matched by a defined message), then it will render a default message
 * using the {@link ngMessageDefault} directive.
 * Note that matched messages will always take precedence over unmatched messages. That means
 * the default message will not be displayed when another message is matched. This is also
 * true for `ng-messages-multiple`.
 *
 * ```html
 * <div ng-messages="myForm.myField.$error" role="alert">
 *   <div ng-message="required">This field is required</div>
 *   <div ng-message="minlength">This field is too short</div>
 *   <div ng-message-default>This field has an input error</div>
 * </div>
 * ```
 *

 */
angular.module('ngMessages', [], function initAngularHelpers() {
  // Access helpers from AngularJS core.
  // Do it inside a `config` block to ensure `window.angular` is available.
  forEach = angular.forEach;
  isArray = angular.isArray;
  isString = angular.isString;
  jqLite = angular.element;
})
  .info({ angularVersion: '1.8.3' })

  /**
   * @ngdoc directive
   * @module ngMessages
   * @name ngMessages
   * @restrict AE
   *
   * @description
   * `ngMessages` is a directive that is designed to show and hide messages based on the state
   * of a key/value object that it listens on. The directive itself complements error message
   * reporting with the `ngModel` $error object (which stores a key/value state of validation errors).
   *
   * `ngMessages` manages the state of internal messages within its container element. The internal
   * messages use the `ngMessage` directive and will be inserted/removed from the page depending
   * on if they're present within the key/value object. By default, only one message will be displayed
   * at a time and this depends on the prioritization of the messages within the template. (This can
   * be changed by using the `ng-messages-multiple` or `multiple` attribute on the directive container.)
   *
   * A remote template can also be used (With {@link ngMessagesInclude}) to promote message
   * reusability and messages can also be overridden.
   *
   * A default message can also be displayed when no `ngMessage` directive is inserted, using the
   * {@link ngMessageDefault} directive.
   *
   * {@link module:ngMessages Click here} to learn more about `ngMessages` and `ngMessage`.
   *
   * @usage
   * ```html
   * <!-- using attribute directives -->
   * <ANY ng-messages="expression" role="alert">
   *   <ANY ng-message="stringValue">...</ANY>
   *   <ANY ng-message="stringValue1, stringValue2, ...">...</ANY>
   *   <ANY ng-message-exp="expressionValue">...</ANY>
   *   <ANY ng-message-default>...</ANY>
   * </ANY>
   *
   * <!-- or by using element directives -->
   * <ng-messages for="expression" role="alert">
   *   <ng-message when="stringValue">...</ng-message>
   *   <ng-message when="stringValue1, stringValue2, ...">...</ng-message>
   *   <ng-message when-exp="expressionValue">...</ng-message>
   *   <ng-message-default>...</ng-message-default>
   * </ng-messages>
   * ```
   *
   * @param {string} ngMessages an AngularJS expression evaluating to a key/value object
   *                 (this is typically the $error object on an ngModel instance).
   * @param {string=} ngMessagesMultiple|multiple when set, all messages will be displayed with true
   *
   * @example
   * <example name="ngMessages-directive" module="ngMessagesExample"
   *          deps="angular-messages.js"
   *          animations="true" fixBase="true">
   *   <file name="index.html">
   *     <form name="myForm">
   *       <label>
   *         Enter your name:
   *         <input type="text"
   *                name="myName"
   *                ng-model="name"
   *                ng-minlength="5"
   *                ng-maxlength="20"
   *                required />
   *       </label>
   *       <pre>myForm.myName.$error = {{ myForm.myName.$error | json }}</pre>
   *
   *       <div ng-messages="myForm.myName.$error" style="color:maroon" role="alert">
   *         <div ng-message="required">You did not enter a field</div>
   *         <div ng-message="minlength">Your field is too short</div>
   *         <div ng-message="maxlength">Your field is too long</div>
   *         <div ng-message-default>This field has an input error</div>
   *       </div>
   *     </form>
   *   </file>
   *   <file name="script.js">
   *     angular.module('ngMessagesExample', ['ngMessages']);
   *   </file>
   * </example>
   */
  .directive('ngMessages', ['$animate', function($animate) {
    var ACTIVE_CLASS = 'ng-active';
    var INACTIVE_CLASS = 'ng-inactive';

    return {
      require: 'ngMessages',
      restrict: 'AE',
      controller: ['$element', '$scope', '$attrs', function NgMessagesCtrl($element, $scope, $attrs) {
        var ctrl = this;
        var latestKey = 0;
        var nextAttachId = 0;

        this.getAttachId = function getAttachId() { return nextAttachId++; };

        var messages = this.messages = {};
        var renderLater, cachedCollection;

        this.render = function(collection) {
          collection = collection || {};

          renderLater = false;
          cachedCollection = collection;

          // this is true if the attribute is empty or if the attribute value is truthy
          var multiple = isAttrTruthy($scope, $attrs.ngMessagesMultiple) ||
                         isAttrTruthy($scope, $attrs.multiple);

          var unmatchedMessages = [];
          var matchedKeys = {};
          var truthyKeys = 0;
          var messageItem = ctrl.head;
          var messageFound = false;
          var totalMessages = 0;

          // we use != instead of !== to allow for both undefined and null values
          while (messageItem != null) {
            totalMessages++;
            var messageCtrl = messageItem.message;

            var messageUsed = false;
            if (!messageFound) {
              forEach(collection, function(value, key) {
                if (truthy(value) && !messageUsed) {
                  truthyKeys++;

                  if (messageCtrl.test(key)) {
                    // this is to prevent the same error name from showing up twice
                    if (matchedKeys[key]) return;
                    matchedKeys[key] = true;

                    messageUsed = true;
                    messageCtrl.attach();
                  }
                }
              });
            }

            if (messageUsed) {
              // unless we want to display multiple messages then we should
              // set a flag here to avoid displaying the next message in the list
              messageFound = !multiple;
            } else {
              unmatchedMessages.push(messageCtrl);
            }

            messageItem = messageItem.next;
          }

          forEach(unmatchedMessages, function(messageCtrl) {
            messageCtrl.detach();
          });

          var messageMatched = unmatchedMessages.length !== totalMessages;
          var attachDefault = ctrl.default && !messageMatched && truthyKeys > 0;

          if (attachDefault) {
            ctrl.default.attach();
          } else if (ctrl.default) {
            ctrl.default.detach();
          }

          if (messageMatched || attachDefault) {
            $animate.setClass($element, ACTIVE_CLASS, INACTIVE_CLASS);
          } else {
            $animate.setClass($element, INACTIVE_CLASS, ACTIVE_CLASS);
          }
        };

        $scope.$watchCollection($attrs.ngMessages || $attrs['for'], ctrl.render);

        this.reRender = function() {
          if (!renderLater) {
            renderLater = true;
            $scope.$evalAsync(function() {
              if (renderLater && cachedCollection) {
                ctrl.render(cachedCollection);
              }
            });
          }
        };

        this.register = function(comment, messageCtrl, isDefault) {
          if (isDefault) {
            ctrl.default = messageCtrl;
          } else {
            var nextKey = latestKey.toString();
            messages[nextKey] = {
              message: messageCtrl
            };
            insertMessageNode($element[0], comment, nextKey);
            comment.$$ngMessageNode = nextKey;
            latestKey++;
          }

          ctrl.reRender();
        };

        this.deregister = function(comment, isDefault) {
          if (isDefault) {
            delete ctrl.default;
          } else {
            var key = comment.$$ngMessageNode;
            delete comment.$$ngMessageNode;
            removeMessageNode($element[0], comment, key);
            delete messages[key];
          }
          ctrl.reRender();
        };

        function findPreviousMessage(parent, comment) {
          var prevNode = comment;
          var parentLookup = [];

          while (prevNode && prevNode !== parent) {
            var prevKey = prevNode.$$ngMessageNode;
            if (prevKey && prevKey.length) {
              return messages[prevKey];
            }

            // dive deeper into the DOM and examine its children for any ngMessage
            // comments that may be in an element that appears deeper in the list
            if (prevNode.childNodes.length && parentLookup.indexOf(prevNode) === -1) {
              parentLookup.push(prevNode);
              prevNode = prevNode.childNodes[prevNode.childNodes.length - 1];
            } else if (prevNode.previousSibling) {
              prevNode = prevNode.previousSibling;
            } else {
              prevNode = prevNode.parentNode;
              parentLookup.push(prevNode);
            }
          }
        }

        function insertMessageNode(parent, comment, key) {
          var messageNode = messages[key];
          if (!ctrl.head) {
            ctrl.head = messageNode;
          } else {
            var match = findPreviousMessage(parent, comment);
            if (match) {
              messageNode.next = match.next;
              match.next = messageNode;
            } else {
              messageNode.next = ctrl.head;
              ctrl.head = messageNode;
            }
          }
        }

        function removeMessageNode(parent, comment, key) {
          var messageNode = messages[key];

          // This message node may have already been removed by a call to deregister()
          if (!messageNode) return;

          var match = findPreviousMessage(parent, comment);
          if (match) {
            match.next = messageNode.next;
          } else {
            ctrl.head = messageNode.next;
          }
        }
      }]
    };

    function isAttrTruthy(scope, attr) {
     return (isString(attr) && attr.length === 0) || //empty attribute
            truthy(scope.$eval(attr));
    }

    function truthy(val) {
      return isString(val) ? val.length : !!val;
    }
  }])

  /**
   * @ngdoc directive
   * @name ngMessagesInclude
   * @restrict AE
   * @scope
   *
   * @description
   * `ngMessagesInclude` is a directive with the purpose to import existing ngMessage template
   * code from a remote template and place the downloaded template code into the exact spot
   * that the ngMessagesInclude directive is placed within the ngMessages container. This allows
   * for a series of pre-defined messages to be reused and also allows for the developer to
   * determine what messages are overridden due to the placement of the ngMessagesInclude directive.
   *
   * @usage
   * ```html
   * <!-- using attribute directives -->
   * <ANY ng-messages="expression" role="alert">
   *   <ANY ng-messages-include="remoteTplString">...</ANY>
   * </ANY>
   *
   * <!-- or by using element directives -->
   * <ng-messages for="expression" role="alert">
   *   <ng-messages-include src="expressionValue1">...</ng-messages-include>
   * </ng-messages>
   * ```
   *
   * {@link module:ngMessages Click here} to learn more about `ngMessages` and `ngMessage`.
   *
   * @param {string} ngMessagesInclude|src a string value corresponding to the remote template.
   */
  .directive('ngMessagesInclude',
    ['$templateRequest', '$document', '$compile', function($templateRequest, $document, $compile) {

    return {
      restrict: 'AE',
      require: '^^ngMessages', // we only require this for validation sake
      link: function($scope, element, attrs) {
        var src = attrs.ngMessagesInclude || attrs.src;
        $templateRequest(src).then(function(html) {
          if ($scope.$$destroyed) return;

          if (isString(html) && !html.trim()) {
            // Empty template - nothing to compile
            replaceElementWithMarker(element, src);
          } else {
            // Non-empty template - compile and link
            $compile(html)($scope, function(contents) {
              element.after(contents);
              replaceElementWithMarker(element, src);
            });
          }
        });
      }
    };

    // Helpers
    function replaceElementWithMarker(element, src) {
      // A comment marker is placed for debugging purposes
      var comment = $compile.$$createComment ?
          $compile.$$createComment('ngMessagesInclude', src) :
          $document[0].createComment(' ngMessagesInclude: ' + src + ' ');
      var marker = jqLite(comment);
      element.after(marker);

      // Don't pollute the DOM anymore by keeping an empty directive element
      element.remove();
    }
  }])

  /**
   * @ngdoc directive
   * @name ngMessage
   * @restrict AE
   * @scope
   * @priority 1
   *
   * @description
   * `ngMessage` is a directive with the purpose to show and hide a particular message.
   * For `ngMessage` to operate, a parent `ngMessages` directive on a parent DOM element
   * must be situated since it determines which messages are visible based on the state
   * of the provided key/value map that `ngMessages` listens on.
   *
   * More information about using `ngMessage` can be found in the
   * {@link module:ngMessages `ngMessages` module documentation}.
   *
   * @usage
   * ```html
   * <!-- using attribute directives -->
   * <ANY ng-messages="expression" role="alert">
   *   <ANY ng-message="stringValue">...</ANY>
   *   <ANY ng-message="stringValue1, stringValue2, ...">...</ANY>
   * </ANY>
   *
   * <!-- or by using element directives -->
   * <ng-messages for="expression" role="alert">
   *   <ng-message when="stringValue">...</ng-message>
   *   <ng-message when="stringValue1, stringValue2, ...">...</ng-message>
   * </ng-messages>
   * ```
   *
   * @param {expression} ngMessage|when a string value corresponding to the message key.
   */
  .directive('ngMessage', ngMessageDirectiveFactory())


  /**
   * @ngdoc directive
   * @name ngMessageExp
   * @restrict AE
   * @priority 1
   * @scope
   *
   * @description
   * `ngMessageExp` is the same as {@link directive:ngMessage `ngMessage`}, but instead of a static
   * value, it accepts an expression to be evaluated for the message key.
   *
   * @usage
   * ```html
   * <!-- using attribute directives -->
   * <ANY ng-messages="expression">
   *   <ANY ng-message-exp="expressionValue">...</ANY>
   * </ANY>
   *
   * <!-- or by using element directives -->
   * <ng-messages for="expression">
   *   <ng-message when-exp="expressionValue">...</ng-message>
   * </ng-messages>
   * ```
   *
   * {@link module:ngMessages Click here} to learn more about `ngMessages` and `ngMessage`.
   *
   * @param {expression} ngMessageExp|whenExp an expression value corresponding to the message key.
   */
  .directive('ngMessageExp', ngMessageDirectiveFactory())

  /**
   * @ngdoc directive
   * @name ngMessageDefault
   * @restrict AE
   * @scope
   *
   * @description
   * `ngMessageDefault` is a directive with the purpose to show and hide a default message for
   * {@link directive:ngMessages}, when none of provided messages matches.
   *
   * More information about using `ngMessageDefault` can be found in the
   * {@link module:ngMessages `ngMessages` module documentation}.
   *
   * @usage
   * ```html
   * <!-- using attribute directives -->
   * <ANY ng-messages="expression" role="alert">
   *   <ANY ng-message="stringValue">...</ANY>
   *   <ANY ng-message="stringValue1, stringValue2, ...">...</ANY>
   *   <ANY ng-message-default>...</ANY>
   * </ANY>
   *
   * <!-- or by using element directives -->
   * <ng-messages for="expression" role="alert">
   *   <ng-message when="stringValue">...</ng-message>
   *   <ng-message when="stringValue1, stringValue2, ...">...</ng-message>
   *   <ng-message-default>...</ng-message-default>
   * </ng-messages>
   *
  */
  .directive('ngMessageDefault', ngMessageDirectiveFactory(true));

function ngMessageDirectiveFactory(isDefault) {
  return ['$animate', function($animate) {
    return {
      restrict: 'AE',
      transclude: 'element',
      priority: 1, // must run before ngBind, otherwise the text is set on the comment
      terminal: true,
      require: '^^ngMessages',
      link: function(scope, element, attrs, ngMessagesCtrl, $transclude) {
        var commentNode, records, staticExp, dynamicExp;

        if (!isDefault) {
          commentNode = element[0];
          staticExp = attrs.ngMessage || attrs.when;
          dynamicExp = attrs.ngMessageExp || attrs.whenExp;

          var assignRecords = function(items) {
            records = items
                ? (isArray(items)
                      ? items
                      : items.split(/[\s,]+/))
                : null;
            ngMessagesCtrl.reRender();
          };

          if (dynamicExp) {
            assignRecords(scope.$eval(dynamicExp));
            scope.$watchCollection(dynamicExp, assignRecords);
          } else {
            assignRecords(staticExp);
          }
        }

        var currentElement, messageCtrl;
        ngMessagesCtrl.register(commentNode, messageCtrl = {
          test: function(name) {
            return contains(records, name);
          },
          attach: function() {
            if (!currentElement) {
              $transclude(function(elm, newScope) {
                $animate.enter(elm, null, element);
                currentElement = elm;

                // Each time we attach this node to a message we get a new id that we can match
                // when we are destroying the node later.
                var $$attachId = currentElement.$$attachId = ngMessagesCtrl.getAttachId();

                // in the event that the element or a parent element is destroyed
                // by another structural directive then it's time
                // to deregister the message from the controller
                currentElement.on('$destroy', function() {
                  // If the message element was removed via a call to `detach` then `currentElement` will be null
                  // So this handler only handles cases where something else removed the message element.
                  if (currentElement && currentElement.$$attachId === $$attachId) {
                    ngMessagesCtrl.deregister(commentNode, isDefault);
                    messageCtrl.detach();
                  }
                  newScope.$destroy();
                });
              });
            }
          },
          detach: function() {
            if (currentElement) {
              var elm = currentElement;
              currentElement = null;
              $animate.leave(elm);
            }
          }
        }, isDefault);

        // We need to ensure that this directive deregisters itself when it no longer exists
        // Normally this is done when the attached element is destroyed; but if this directive
        // gets removed before we attach the message to the DOM there is nothing to watch
        // in which case we must deregister when the containing scope is destroyed.
        scope.$on('$destroy', function() {
          ngMessagesCtrl.deregister(commentNode, isDefault);
        });
      }
    };
  }];

  function contains(collection, key) {
    if (collection) {
      return isArray(collection)
          ? collection.indexOf(key) >= 0
          : collection.hasOwnProperty(key);
    }
  }
}


})(window, window.angular);

/*!
 * AngularJS Material Design
 * https://github.com/angular/material
 * @license MIT
 * v1.1.24
 */
(function( window, angular, undefined ){
"use strict";

(function(){
"use strict";

angular.module('ngMaterial', ["ng","ngAnimate","ngAria","material.core","material.core.animate","material.core.gestures","material.core.interaction","material.core.layout","material.core.meta","material.core.theming.palette","material.core.theming","material.components.autocomplete","material.components.backdrop","material.components.bottomSheet","material.components.button","material.components.card","material.components.checkbox","material.components.chips","material.components.colors","material.components.content","material.components.datepicker","material.components.dialog","material.components.divider","material.components.fabActions","material.components.fabShared","material.components.fabSpeedDial","material.components.fabToolbar","material.components.gridList","material.components.icon","material.components.input","material.components.list","material.components.menu","material.components.menuBar","material.components.navBar","material.components.panel","material.components.progressCircular","material.components.progressLinear","material.components.radioButton","material.components.select","material.components.showHide","material.components.sidenav","material.components.slider","material.components.sticky","material.components.subheader","material.components.swipe","material.components.switch","material.components.tabs","material.components.toast","material.components.toolbar","material.components.tooltip","material.components.truncate","material.components.virtualRepeat","material.components.whiteframe"]);
})();
(function(){
"use strict";

/**
 * Initialization function that validates environment
 * requirements.
 */
DetectNgTouch.$inject = ["$log", "$injector"];
MdCoreConfigure.$inject = ["$provide", "$mdThemingProvider"];
rAFDecorator.$inject = ["$delegate"];
qDecorator.$inject = ["$delegate"];
angular
  .module('material.core', [
    'ngAnimate',
    'material.core.animate',
    'material.core.layout',
    'material.core.interaction',
    'material.core.gestures',
    'material.core.theming'
  ])
  .config(MdCoreConfigure)
  .run(DetectNgTouch);


/**
 * Detect if the ng-Touch module is also being used.
 * Warn if detected.
 * @ngInject
 */
function DetectNgTouch($log, $injector) {
  if ($injector.has('$swipe')) {
    var msg = "" +
      "You are using the ngTouch module. \n" +
      "AngularJS Material already has mobile click, tap, and swipe support... \n" +
      "ngTouch is not supported with AngularJS Material!";
    $log.warn(msg);
  }
}

/**
 * @ngInject
 */
function MdCoreConfigure($provide, $mdThemingProvider) {

  $provide.decorator('$$rAF', ['$delegate', rAFDecorator]);
  $provide.decorator('$q', ['$delegate', qDecorator]);

  $mdThemingProvider.theme('default')
    .primaryPalette('indigo')
    .accentPalette('pink')
    .warnPalette('deep-orange')
    .backgroundPalette('grey');
}

/**
 * @ngInject
 */
function rAFDecorator($delegate) {
  /**
   * Use this to throttle events that come in often.
   * The throttled function will always use the *last* invocation before the
   * coming frame.
   *
   * For example, window resize events that fire many times a second:
   * If we set to use an raf-throttled callback on window resize, then
   * our callback will only be fired once per frame, with the last resize
   * event that happened before that frame.
   *
   * @param {function} callback function to debounce
   */
  $delegate.throttle = function(cb) {
    var queuedArgs, alreadyQueued, queueCb, context;
    return function debounced() {
      queuedArgs = arguments;
      context = this;
      queueCb = cb;
      if (!alreadyQueued) {
        alreadyQueued = true;
        $delegate(function() {
          queueCb.apply(context, Array.prototype.slice.call(queuedArgs));
          alreadyQueued = false;
        });
      }
    };
  };
  return $delegate;
}

/**
 * @ngInject
 */
function qDecorator($delegate) {
  /**
   * Adds a shim for $q.resolve for AngularJS version that don't have it,
   * so we don't have to think about it.
   *
   * via https://github.com/angular/angular.js/pull/11987
   */

  // TODO(crisbeto): this won't be necessary once we drop AngularJS 1.3
  if (!$delegate.resolve) {
    $delegate.resolve = $delegate.when;
  }
  return $delegate;
}

})();
(function(){
"use strict";


MdAutofocusDirective.$inject = ["$parse"];angular.module('material.core')
  .directive('mdAutofocus', MdAutofocusDirective)

  // Support the deprecated md-auto-focus and md-sidenav-focus as well
  .directive('mdAutoFocus', MdAutofocusDirective)
  .directive('mdSidenavFocus', MdAutofocusDirective);

/**
 * @ngdoc directive
 * @name mdAutofocus
 * @module material.core.util
 *
 * @description
 *
 * `[md-autofocus]` provides an optional way to identify the focused element when a `$mdDialog`,
 * `$mdBottomSheet`, `$mdMenu` or `$mdSidenav` opens or upon page load for input-like elements.
 *
 * When one of these opens, it will find the first nested element with the `[md-autofocus]`
 * attribute directive and optional expression. An expression may be specified as the directive
 * value to enable conditional activation of the autofocus.
 *
 * @usage
 *
 * ### Dialog
 * <hljs lang="html">
 * <md-dialog>
 *   <form>
 *     <md-input-container>
 *       <label for="testInput">Label</label>
 *       <input id="testInput" type="text" md-autofocus>
 *     </md-input-container>
 *   </form>
 * </md-dialog>
 * </hljs>
 *
 * ### Bottomsheet
 * <hljs lang="html">
 * <md-bottom-sheet class="md-list md-has-header">
 *  <md-subheader>Comment Actions</md-subheader>
 *  <md-list>
 *    <md-list-item ng-repeat="item in items">
 *
 *      <md-button md-autofocus="$index == 2">
 *        <md-icon md-svg-src="{{item.icon}}"></md-icon>
 *        <span class="md-inline-list-icon-label">{{ item.name }}</span>
 *      </md-button>
 *
 *    </md-list-item>
 *  </md-list>
 * </md-bottom-sheet>
 * </hljs>
 *
 * ### Autocomplete
 * <hljs lang="html">
 *   <md-autocomplete
 *       md-autofocus
 *       md-selected-item="selectedItem"
 *       md-search-text="searchText"
 *       md-items="item in getMatches(searchText)"
 *       md-item-text="item.display">
 *     <span md-highlight-text="searchText">{{item.display}}</span>
 *   </md-autocomplete>
 * </hljs>
 *
 * ### Sidenav
 * <hljs lang="html">
 * <div layout="row" ng-controller="MyController">
 *   <md-sidenav md-component-id="left" class="md-sidenav-left">
 *     Left Nav!
 *   </md-sidenav>
 *
 *   <md-content>
 *     Center Content
 *     <md-button ng-click="openLeftMenu()">
 *       Open Left Menu
 *     </md-button>
 *   </md-content>
 *
 *   <md-sidenav md-component-id="right"
 *     md-is-locked-open="$mdMedia('min-width: 333px')"
 *     class="md-sidenav-right">
 *     <form>
 *       <md-input-container>
 *         <label for="testInput">Test input</label>
 *         <input id="testInput" type="text"
 *                ng-model="data" md-autofocus>
 *       </md-input-container>
 *     </form>
 *   </md-sidenav>
 * </div>
 * </hljs>
 **/
function MdAutofocusDirective($parse) {
  return {
    restrict: 'A',
    link: {
      pre: preLink
    }
  };

  function preLink(scope, element, attr) {
    var attrExp = attr.mdAutoFocus || attr.mdAutofocus || attr.mdSidenavFocus;

    // Initially update the expression by manually parsing the expression as per $watch source.
    updateExpression($parse(attrExp)(scope));

    // Only watch the expression if it is not empty.
    if (attrExp) {
      scope.$watch(attrExp, updateExpression);
    }

    /**
     * Updates the autofocus class which is used to determine whether the attribute
     * expression evaluates to true or false.
     * @param {string|boolean} value Attribute Value
     */
    function updateExpression(value) {

      // Rather than passing undefined to the jqLite toggle class function we explicitly set the
      // value to true. Otherwise the class will be just toggled instead of being forced.
      if (angular.isUndefined(value)) {
        value = true;
      }

      element.toggleClass('md-autofocus', !!value);
    }
  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.colorUtil
 * @description
 * Color Util
 */
angular
  .module('material.core')
  .factory('$mdColorUtil', ColorUtilFactory);

function ColorUtilFactory() {
  /**
   * Converts hex value to RGBA string
   * @param color {string}
   * @returns {string}
   */
  function hexToRgba (color) {
    var hex   = color[ 0 ] === '#' ? color.substr(1) : color,
      dig   = hex.length / 3,
      red   = hex.substr(0, dig),
      green = hex.substr(dig, dig),
      blue  = hex.substr(dig * 2);
    if (dig === 1) {
      red += red;
      green += green;
      blue += blue;
    }
    return 'rgba(' + parseInt(red, 16) + ',' + parseInt(green, 16) + ',' + parseInt(blue, 16) + ',0.1)';
  }

  /**
   * Converts rgba value to hex string
   * @param {string} color
   * @returns {string}
   */
  function rgbaToHex(color) {
    color = color.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);

    var hex = (color && color.length === 4) ? "#" +
    ("0" + parseInt(color[1],10).toString(16)).slice(-2) +
    ("0" + parseInt(color[2],10).toString(16)).slice(-2) +
    ("0" + parseInt(color[3],10).toString(16)).slice(-2) : '';

    return hex.toUpperCase();
  }

  /**
   * Converts an RGB color to RGBA
   * @param {string} color
   * @returns {string}
   */
  function rgbToRgba (color) {
    return color.replace(')', ', 0.1)').replace('(', 'a(');
  }

  /**
   * Converts an RGBA color to RGB
   * @param {string} color
   * @returns {string}
   */
  function rgbaToRgb (color) {
    return color
      ? color.replace('rgba', 'rgb').replace(/,[^),]+\)/, ')')
      : 'rgb(0,0,0)';
  }

  return {
    rgbaToHex: rgbaToHex,
    hexToRgba: hexToRgba,
    rgbToRgba: rgbToRgba,
    rgbaToRgb: rgbaToRgb
  };
}

})();
(function(){
"use strict";

angular.module('material.core')
.factory('$mdConstant', MdConstantFactory);

/**
 * Factory function that creates the grab-bag $mdConstant service.
 * @ngInject
 */
function MdConstantFactory() {

  var prefixTestEl = document.createElement('div');
  var vendorPrefix = getVendorPrefix(prefixTestEl);
  var isWebkit = /webkit/i.test(vendorPrefix);
  var SPECIAL_CHARS_REGEXP = /([:\-_]+(.))/g;

  /**
   * @param {string} name CSS property name
   * @return {string} the property name supported by the browser
   */
  function vendorProperty(name) {
    // Add a dash between the prefix and name, to be able to transform the string into camelcase.
    var prefixedName = vendorPrefix + '-' + name;
    var ucPrefix = camelCase(prefixedName);
    var lcPrefix = ucPrefix.charAt(0).toLowerCase() + ucPrefix.substring(1);

    return hasStyleProperty(prefixTestEl, name)     ? name     :       // The current browser supports the un-prefixed property
           hasStyleProperty(prefixTestEl, ucPrefix) ? ucPrefix :       // The current browser only supports the prefixed property.
           hasStyleProperty(prefixTestEl, lcPrefix) ? lcPrefix : name; // Some browsers are only supporting the prefix in lowercase.
  }

  function hasStyleProperty(testElement, property) {
    return angular.isDefined(testElement.style[property]);
  }

  /**
   * @param {!string} input value to convert to camelCase
   * @return {string} camelCased version of the input string
   */
  function camelCase(input) {
    return input.replace(SPECIAL_CHARS_REGEXP, function(matches, separator, letter, offset) {
      return offset ? letter.toUpperCase() : letter;
    });
  }

  function getVendorPrefix(testElement) {
    var prop, match;
    var vendorRegex = /^(Moz|webkit|ms)(?=[A-Z])/;

    for (prop in testElement.style) {
      if (match = vendorRegex.exec(prop)) {
        return match[0];
      }
    }
  }

  var self = {
    isInputKey : function(e) { return (e.keyCode >= 31 && e.keyCode <= 90); },
    isNumPadKey : function(e) { return (3 === e.location && e.keyCode >= 97 && e.keyCode <= 105); },
    isMetaKey: function(e) { return (e.keyCode >= 91 && e.keyCode <= 93); },
    isFnLockKey: function(e) { return (e.keyCode >= 112 && e.keyCode <= 145); },
    isNavigationKey : function(e) {
      var kc = self.KEY_CODE, NAVIGATION_KEYS =  [kc.SPACE, kc.ENTER, kc.UP_ARROW, kc.DOWN_ARROW];
      return (NAVIGATION_KEYS.indexOf(e.keyCode) != -1);
    },
    hasModifierKey: function(e) {
      return e.ctrlKey || e.metaKey || e.altKey;
    },

    /**
     * Maximum size, in pixels, that can be explicitly set to an element. The actual value varies
     * between browsers, but IE11 has the very lowest size at a mere 1,533,917px. Ideally we could
     * compute this value, but Firefox always reports an element to have a size of zero if it
     * goes over the max, meaning that we'd have to binary search for the value.
     */
    ELEMENT_MAX_PIXELS: 1533917,

    /**
     * Priority for a directive that should run before the directives from ngAria.
     */
    BEFORE_NG_ARIA: 210,

    /**
     * Common Keyboard actions and their associated keycode.
     */
    KEY_CODE: {
      COMMA: 188,
      SEMICOLON : 186,
      ENTER: 13,
      ESCAPE: 27,
      SPACE: 32,
      PAGE_UP: 33,
      PAGE_DOWN: 34,
      END: 35,
      HOME: 36,
      LEFT_ARROW : 37,
      UP_ARROW : 38,
      RIGHT_ARROW : 39,
      DOWN_ARROW : 40,
      TAB : 9,
      BACKSPACE: 8,
      DELETE: 46
    },

    /**
     * Vendor prefixed CSS properties to be used to support the given functionality in older browsers
     * as well.
     */
    CSS: {
      /* Constants */
      TRANSITIONEND: 'transitionend' + (isWebkit ? ' webkitTransitionEnd' : ''),
      ANIMATIONEND: 'animationend' + (isWebkit ? ' webkitAnimationEnd' : ''),

      TRANSFORM: vendorProperty('transform'),
      TRANSFORM_ORIGIN: vendorProperty('transformOrigin'),
      TRANSITION: vendorProperty('transition'),
      TRANSITION_DURATION: vendorProperty('transitionDuration'),
      ANIMATION_PLAY_STATE: vendorProperty('animationPlayState'),
      ANIMATION_DURATION: vendorProperty('animationDuration'),
      ANIMATION_NAME: vendorProperty('animationName'),
      ANIMATION_TIMING: vendorProperty('animationTimingFunction'),
      ANIMATION_DIRECTION: vendorProperty('animationDirection')
    },

    /**
     * As defined in core/style/_variables.scss
     *
     * $layout-breakpoint-xs:     600px !default;
     * $layout-breakpoint-sm:     960px !default;
     * $layout-breakpoint-md:     1280px !default;
     * $layout-breakpoint-lg:     1920px !default;
     *
     */
    MEDIA: {
      'xs'        : '(max-width: 599px)'                         ,
      'gt-xs'     : '(min-width: 600px)'                         ,
      'sm'        : '(min-width: 600px) and (max-width: 959px)'  ,
      'gt-sm'     : '(min-width: 960px)'                         ,
      'md'        : '(min-width: 960px) and (max-width: 1279px)' ,
      'gt-md'     : '(min-width: 1280px)'                        ,
      'lg'        : '(min-width: 1280px) and (max-width: 1919px)',
      'gt-lg'     : '(min-width: 1920px)'                        ,
      'xl'        : '(min-width: 1920px)'                        ,
      'landscape' : '(orientation: landscape)'                   ,
      'portrait'  : '(orientation: portrait)'                    ,
      'print' : 'print'
    },

    MEDIA_PRIORITY: [
      'xl',
      'gt-lg',
      'lg',
      'gt-md',
      'md',
      'gt-sm',
      'sm',
      'gt-xs',
      'xs',
      'landscape',
      'portrait',
      'print'
    ]
  };

  return self;
}

})();
(function(){
"use strict";

  angular
    .module('material.core')
    .config(["$provide", function($provide){
       $provide.decorator('$mdUtil', ['$delegate', function ($delegate){
           /**
            * Inject the iterator facade to easily support iteration and accessors
            * @see iterator below
            */
           $delegate.iterator = MdIterator;

           return $delegate;
         }
       ]);
     }]);

  /**
   * iterator is a list facade to easily support iteration and accessors
   *
   * @param items Array list which this iterator will enumerate
   * @param reloop Boolean enables iterator to consider the list as an endless reloop
   */
  function MdIterator(items, reloop) {
    var trueFn = function() { return true; };

    if (items && !angular.isArray(items)) {
      items = Array.prototype.slice.call(items);
    }

    reloop = !!reloop;
    var _items = items || [];

    // Published API
    return {
      items: getItems,
      count: count,

      inRange: inRange,
      contains: contains,
      indexOf: indexOf,
      itemAt: itemAt,

      findBy: findBy,

      add: add,
      remove: remove,

      first: first,
      last: last,
      next: angular.bind(null, findSubsequentItem, false),
      previous: angular.bind(null, findSubsequentItem, true),

      hasPrevious: hasPrevious,
      hasNext: hasNext

    };

    /**
     * Publish copy of the enumerable set
     * @returns {Array|*}
     */
    function getItems() {
      return [].concat(_items);
    }

    /**
     * Determine length of the list
     * @returns {Array.length|*|number}
     */
    function count() {
      return _items.length;
    }

    /**
     * Is the index specified valid
     * @param index
     * @returns {Array.length|*|number|boolean}
     */
    function inRange(index) {
      return _items.length && (index > -1) && (index < _items.length);
    }

    /**
     * Can the iterator proceed to the next item in the list; relative to
     * the specified item.
     *
     * @param item
     * @returns {Array.length|*|number|boolean}
     */
    function hasNext(item) {
      return item ? inRange(indexOf(item) + 1) : false;
    }

    /**
     * Can the iterator proceed to the previous item in the list; relative to
     * the specified item.
     *
     * @param item
     * @returns {Array.length|*|number|boolean}
     */
    function hasPrevious(item) {
      return item ? inRange(indexOf(item) - 1) : false;
    }

    /**
     * Get item at specified index/position
     * @param index
     * @returns {*}
     */
    function itemAt(index) {
      return inRange(index) ? _items[index] : null;
    }

    /**
     * Find all elements matching the key/value pair
     * otherwise return null
     *
     * @param val
     * @param key
     *
     * @return array
     */
    function findBy(key, val) {
      return _items.filter(function(item) {
        return item[key] === val;
      });
    }

    /**
     * Add item to list
     * @param item
     * @param index
     * @returns {*}
     */
    function add(item, index) {
      if (!item) return -1;

      if (!angular.isNumber(index)) {
        index = _items.length;
      }

      _items.splice(index, 0, item);

      return indexOf(item);
    }

    /**
     * Remove item from list...
     * @param item
     */
    function remove(item) {
      if (contains(item)){
        _items.splice(indexOf(item), 1);
      }
    }

    /**
     * Get the zero-based index of the target item
     * @param item
     * @returns {*}
     */
    function indexOf(item) {
      return _items.indexOf(item);
    }

    /**
     * Boolean existence check
     * @param item
     * @returns {boolean}
     */
    function contains(item) {
      return item && (indexOf(item) > -1);
    }

    /**
     * Return first item in the list
     * @returns {*}
     */
    function first() {
      return _items.length ? _items[0] : null;
    }

    /**
     * Return last item in the list...
     * @returns {*}
     */
    function last() {
      return _items.length ? _items[_items.length - 1] : null;
    }

    /**
     * Find the next item. If reloop is true and at the end of the list, it will go back to the
     * first item. If given, the `validate` callback will be used to determine whether the next item
     * is valid. If not valid, it will try to find the next item again.
     *
     * @param {boolean} backwards Specifies the direction of searching (forwards/backwards)
     * @param {*} item The item whose subsequent item we are looking for
     * @param {Function=} validate The `validate` function
     * @param {integer=} limit The recursion limit
     *
     * @returns {*} The subsequent item or null
     */
    function findSubsequentItem(backwards, item, validate, limit) {
      validate = validate || trueFn;

      var curIndex = indexOf(item);
      while (true) {
        if (!inRange(curIndex)) return null;

        var nextIndex = curIndex + (backwards ? -1 : 1);
        var foundItem = null;
        if (inRange(nextIndex)) {
          foundItem = _items[nextIndex];
        } else if (reloop) {
          foundItem = backwards ? last() : first();
          nextIndex = indexOf(foundItem);
        }

        if ((foundItem === null) || (nextIndex === limit)) return null;
        if (validate(foundItem)) return foundItem;

        if (angular.isUndefined(limit)) limit = nextIndex;

        curIndex = nextIndex;
      }
    }
  }


})();
(function(){
"use strict";


mdMediaFactory.$inject = ["$mdConstant", "$rootScope", "$window"];angular.module('material.core')
.factory('$mdMedia', mdMediaFactory);

/**
 * @ngdoc service
 * @name $mdMedia
 * @module material.core
 *
 * @description
 * `$mdMedia` is used to evaluate whether a given media query is true or false given the
 * current device's screen / window size. The media query will be re-evaluated on resize, allowing
 * you to register a watch.
 *
 * `$mdMedia` also has pre-programmed support for media queries that match the layout breakpoints:
 *
 *  <table class="md-api-table">
 *    <thead>
 *    <tr>
 *      <th>Breakpoint</th>
 *      <th>mediaQuery</th>
 *    </tr>
 *    </thead>
 *    <tbody>
 *    <tr>
 *      <td>xs</td>
 *      <td>(max-width: 599px)</td>
 *    </tr>
 *    <tr>
 *      <td>gt-xs</td>
 *      <td>(min-width: 600px)</td>
 *    </tr>
 *    <tr>
 *      <td>sm</td>
 *      <td>(min-width: 600px) and (max-width: 959px)</td>
 *    </tr>
 *    <tr>
 *      <td>gt-sm</td>
 *      <td>(min-width: 960px)</td>
 *    </tr>
 *    <tr>
 *      <td>md</td>
 *      <td>(min-width: 960px) and (max-width: 1279px)</td>
 *    </tr>
 *    <tr>
 *      <td>gt-md</td>
 *      <td>(min-width: 1280px)</td>
 *    </tr>
 *    <tr>
 *      <td>lg</td>
 *      <td>(min-width: 1280px) and (max-width: 1919px)</td>
 *    </tr>
 *    <tr>
 *      <td>gt-lg</td>
 *      <td>(min-width: 1920px)</td>
 *    </tr>
 *    <tr>
 *      <td>xl</td>
 *      <td>(min-width: 1920px)</td>
 *    </tr>
 *    <tr>
 *      <td>landscape</td>
 *      <td>landscape</td>
 *    </tr>
 *    <tr>
 *      <td>portrait</td>
 *      <td>portrait</td>
 *    </tr>
 *    <tr>
 *      <td>print</td>
 *      <td>print</td>
 *    </tr>
 *    </tbody>
 *  </table>
 *
 *  See Material Design's <a href="https://material.google.com/layout/responsive-ui.html">Layout - Adaptive UI</a> for more details.
 *
 *  <a href="https://material.io/archive/guidelines/layout/responsive-ui.html#">
 *  <img src="https://material-design.storage.googleapis.com/publish/material_v_4/material_ext_publish/0B8olV15J7abPSGFxemFiQVRtb1k/layout_adaptive_breakpoints_01.png" width="100%" height="100%"></img>
 *  </a>
 *
 * @returns {boolean} a boolean representing whether or not the given media query is true or false.
 *
 * @usage
 * <hljs lang="js">
 * app.controller('MyController', function($mdMedia, $scope) {
 *   $scope.$watch(function() { return $mdMedia('lg'); }, function(big) {
 *     $scope.bigScreen = big;
 *   });
 *
 *   $scope.screenIsSmall = $mdMedia('sm');
 *   $scope.customQuery = $mdMedia('(min-width: 1234px)');
 *   $scope.anotherCustom = $mdMedia('max-width: 300px');
 * });
 * </hljs>
 */

/* @ngInject */
function mdMediaFactory($mdConstant, $rootScope, $window) {
  var queries = {};
  var mqls = {};
  var results = {};
  var normalizeCache = {};

  $mdMedia.getResponsiveAttribute = getResponsiveAttribute;
  $mdMedia.getQuery = getQuery;
  $mdMedia.watchResponsiveAttributes = watchResponsiveAttributes;

  return $mdMedia;

  function $mdMedia(query) {
    var validated = queries[query];
    if (angular.isUndefined(validated)) {
      validated = queries[query] = validate(query);
    }

    var result = results[validated];
    if (angular.isUndefined(result)) {
      result = add(validated);
    }

    return result;
  }

  function validate(query) {
    return $mdConstant.MEDIA[query] ||
           ((query.charAt(0) !== '(') ? ('(' + query + ')') : query);
  }

  function add(query) {
    var result = mqls[query];
    if (!result) {
      result = mqls[query] = $window.matchMedia(query);
    }

    result.addListener(onQueryChange);
    return (results[result.media] = !!result.matches);
  }

  function onQueryChange(query) {
    $rootScope.$evalAsync(function() {
      results[query.media] = !!query.matches;
    });
  }

  function getQuery(name) {
    return mqls[name];
  }

  function getResponsiveAttribute(attrs, attrName) {
    for (var i = 0; i < $mdConstant.MEDIA_PRIORITY.length; i++) {
      var mediaName = $mdConstant.MEDIA_PRIORITY[i];
      if (!mqls[queries[mediaName]].matches) {
        continue;
      }

      var normalizedName = getNormalizedName(attrs, attrName + '-' + mediaName);
      if (attrs[normalizedName]) {
        return attrs[normalizedName];
      }
    }

    // fallback on unprefixed
    return attrs[getNormalizedName(attrs, attrName)];
  }

  function watchResponsiveAttributes(attrNames, attrs, watchFn) {
    var unwatchFns = [];
    attrNames.forEach(function(attrName) {
      var normalizedName = getNormalizedName(attrs, attrName);
      if (angular.isDefined(attrs[normalizedName])) {
        unwatchFns.push(
            attrs.$observe(normalizedName, angular.bind(void 0, watchFn, null)));
      }

      for (var mediaName in $mdConstant.MEDIA) {
        normalizedName = getNormalizedName(attrs, attrName + '-' + mediaName);
        if (angular.isDefined(attrs[normalizedName])) {
          unwatchFns.push(
              attrs.$observe(normalizedName, angular.bind(void 0, watchFn, mediaName)));
        }
      }
    });

    return function unwatch() {
      unwatchFns.forEach(function(fn) { fn(); });
    };
  }

  // Improves performance dramatically
  function getNormalizedName(attrs, attrName) {
    return normalizeCache[attrName] ||
        (normalizeCache[attrName] = attrs.$normalize(attrName));
  }
}

})();
(function(){
"use strict";

angular
  .module('material.core')
  .config(["$provide", function($provide) {
    $provide.decorator('$mdUtil', ['$delegate', function ($delegate) {

      // Inject the prefixer into our original $mdUtil service.
      $delegate.prefixer = MdPrefixer;

      return $delegate;
    }]);
  }]);

function MdPrefixer(initialAttributes, buildSelector) {
  var PREFIXES = ['data', 'x'];

  if (initialAttributes) {
    // The prefixer also accepts attributes as a parameter, and immediately builds a list or selector for
    // the specified attributes.
    return buildSelector ? _buildSelector(initialAttributes) : _buildList(initialAttributes);
  }

  return {
    buildList: _buildList,
    buildSelector: _buildSelector,
    hasAttribute: _hasAttribute,
    removeAttribute: _removeAttribute
  };

  function _buildList(attributes) {
    attributes = angular.isArray(attributes) ? attributes : [attributes];

    attributes.forEach(function(item) {
      PREFIXES.forEach(function(prefix) {
        attributes.push(prefix + '-' + item);
      });
    });

    return attributes;
  }

  function _buildSelector(attributes) {
    attributes = angular.isArray(attributes) ? attributes : [attributes];

    return _buildList(attributes)
      .map(function(item) {
        return '[' + item + ']';
      })
      .join(',');
  }

  function _hasAttribute(element, attribute) {
    element = _getNativeElement(element);

    if (!element) {
      return false;
    }

    var prefixedAttrs = _buildList(attribute);

    for (var i = 0; i < prefixedAttrs.length; i++) {
      if (element.hasAttribute(prefixedAttrs[i])) {
        return true;
      }
    }

    return false;
  }

  function _removeAttribute(element, attribute) {
    element = _getNativeElement(element);

    if (!element) {
      return;
    }

    _buildList(attribute).forEach(function(prefixedAttribute) {
      element.removeAttribute(prefixedAttribute);
    });
  }

  /**
   * Transforms a jqLite or DOM element into a HTML element.
   * This is useful when supporting jqLite elements and DOM elements at
   * same time.
   * @param element {JQLite|Element} Element to be parsed
   * @returns {HTMLElement} Parsed HTMLElement
   */
  function _getNativeElement(element) {
    element =  element[0] || element;

    if (element.nodeType) {
      return element;
    }
  }

}

})();
(function(){
"use strict";

/*
 * This var has to be outside the angular factory, otherwise when
 * there are multiple material apps on the same page, each app
 * will create its own instance of this array and the app's IDs
 * will not be unique.
 */
UtilFactory.$inject = ["$document", "$timeout", "$compile", "$rootScope", "$$mdAnimate", "$interpolate", "$log", "$rootElement", "$window", "$$rAF"];
var nextUniqueId = 0, isIos, isAndroid;

// Support material-tools builds.
if (window.navigator) {
  var userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
  isIos = userAgent.match(/ipad|iphone|ipod/i);
  isAndroid = userAgent.match(/android/i);
}

/**
 * @ngdoc module
 * @name material.core.util
 * @description
 * Util
 */
angular
.module('material.core')
.factory('$mdUtil', UtilFactory);

/**
 * @ngInject
 */
function UtilFactory($document, $timeout, $compile, $rootScope, $$mdAnimate, $interpolate, $log,
                     $rootElement, $window, $$rAF) {
  // Setup some core variables for the processTemplate method
  var startSymbol = $interpolate.startSymbol(),
    endSymbol = $interpolate.endSymbol(),
    usesStandardSymbols = ((startSymbol === '{{') && (endSymbol === '}}'));

  // Polyfill document.contains for IE11.
  document.contains || (document.contains = function (node) {
    return document.body.contains(node);
  });

  /**
   * Checks if the target element has the requested style by key
   * @param {DOMElement|JQLite} target Target element
   * @param {string} key Style key
   * @param {string=} expectedVal Optional expected value
   * @returns {boolean} Whether the target element has the style or not
   */
  var hasComputedStyle = function (target, key, expectedVal) {
    var hasValue = false;

    if (target && target.length) {
      var computedStyles = $window.getComputedStyle(target[0]);
      hasValue = angular.isDefined(computedStyles[key]) &&
        (expectedVal ? computedStyles[key] == expectedVal : true);
    }

    return hasValue;
  };

  function validateCssValue(value) {
    return !value ? '0' :
      hasPx(value) || hasPercent(value) ? value : value + 'px';
  }

  function hasPx(value) {
    return String(value).indexOf('px') > -1;
  }

  function hasPercent(value) {
    return String(value).indexOf('%') > -1;
  }

  var $mdUtil = {
    dom: {},
    isIos: isIos,
    isAndroid: isAndroid,
    now: window.performance && window.performance.now ?
      angular.bind(window.performance, window.performance.now) : Date.now || function() {
      return new Date().getTime();
    },

    /**
     * Cross-version compatibility method to retrieve an option of a ngModel controller,
     * which supports the breaking changes in the AngularJS snapshot (SHA 87a2ff76af5d0a9268d8eb84db5755077d27c84c).
     * @param {!ngModel.NgModelController} ngModelCtrl
     * @param {!string} optionName
     * @returns {string|number|boolean|Object|undefined}
     */
    getModelOption: function (ngModelCtrl, optionName) {
      if (!ngModelCtrl.$options) {
        return;
      }

      var $options = ngModelCtrl.$options;

      // The newer versions of AngularJS introduced a getOption function and made the option values
      // no longer visible on the $options object.
      return $options.getOption ? $options.getOption(optionName) : $options[optionName];
    },

    /**
     * Determines the current 'dir'ectional value based on the value of 'dir'
     * attribute of the element. If that is not defined, it will try to use
     * a 'dir' attribute of the body or html tag.
     *
     * @param {Object=} attrs a hash object with key-value pairs of normalized
     *     attribute names and their corresponding attribute values.
     * @returns {boolean} true if the element's passed in attributes,
     *     the document, or the body indicates RTL mode, false otherwise.
     */
    isRtl: function(attrs) {
      var dir = angular.isDefined(attrs) && attrs.hasOwnProperty('dir') && attrs.dir;

      switch (dir) {
        case 'ltr':
          return false;

        case 'rtl':
          return true;
      }

      return ($document[0].dir === 'rtl' || $document[0].body.dir === 'rtl');
    },

    /**
     * Bi-directional accessor/mutator used to easily update an element's
     * property based on the current 'dir'ectional value.
     */
    bidi: function(element, property, lValue, rValue) {
      var ltr = !this.isRtl();

      // If accessor
      if (arguments.length == 0) return ltr ? 'ltr' : 'rtl';

      // If mutator
      var elem = angular.element(element);

      if (ltr && angular.isDefined(lValue)) {
        elem.css(property, validateCssValue(lValue));
      }
      else if (!ltr && angular.isDefined(rValue)) {
        elem.css(property, validateCssValue(rValue));
      }
    },

    bidiProperty: function (element, lProperty, rProperty, value) {
      var ltr = !this.isRtl();

      var elem = angular.element(element);

      if (ltr && angular.isDefined(lProperty)) {
        elem.css(lProperty, validateCssValue(value));
        elem.css(rProperty, '');
      }
      else if (!ltr && angular.isDefined(rProperty)) {
        elem.css(rProperty, validateCssValue(value));
        elem.css(lProperty, '');
      }
    },

    clientRect: function(element, offsetParent, isOffsetRect) {
      var node = getNode(element);
      offsetParent = getNode(offsetParent || node.offsetParent || document.body);
      var nodeRect = node.getBoundingClientRect();

      // The user can ask for an offsetRect: a rect relative to the offsetParent,
      // or a clientRect: a rect relative to the page
      var offsetRect = isOffsetRect ?
        offsetParent.getBoundingClientRect() :
        {left: 0, top: 0, width: 0, height: 0};
      return {
        left: nodeRect.left - offsetRect.left,
        top: nodeRect.top - offsetRect.top,
        width: nodeRect.width,
        height: nodeRect.height
      };
    },
    offsetRect: function(element, offsetParent) {
      return $mdUtil.clientRect(element, offsetParent, true);
    },

    /**
     * Annoying method to copy nodes to an array, thanks to IE.
     * @param nodes
     * @return {Array}
     */
    nodesToArray: function(nodes) {
      var results = [], i;
      nodes = nodes || [];

      for (i = 0; i < nodes.length; ++i) {
        results.push(nodes.item(i));
      }
      return results;
    },

    /**
     * Determines the absolute position of the viewport.
     * Useful when making client rectangles absolute.
     * @returns {number}
     */
    getViewportTop: function() {
      // If body scrolling is disabled, then use the cached viewport top value, otherwise get it
      // fresh from the $window.
      if ($mdUtil.disableScrollAround._count && $mdUtil.disableScrollAround._viewPortTop) {
        return $mdUtil.disableScrollAround._viewPortTop;
      } else {
        return $window.scrollY || $window.pageYOffset || 0;
      }
    },

    /**
     * Finds the proper focus target by searching the DOM.
     *
     * @param containerEl
     * @param attributeVal
     * @returns {*}
     */
    findFocusTarget: function(containerEl, attributeVal) {
      var AUTO_FOCUS = this.prefixer('md-autofocus', true);
      var elToFocus;

      elToFocus = scanForFocusable(containerEl, attributeVal || AUTO_FOCUS);

      if (!elToFocus && attributeVal != AUTO_FOCUS) {
        // Scan for deprecated attribute
        elToFocus = scanForFocusable(containerEl, this.prefixer('md-auto-focus', true));

        if (!elToFocus) {
          // Scan for fallback to 'universal' API
          elToFocus = scanForFocusable(containerEl, AUTO_FOCUS);
        }
      }

      return elToFocus;

      /**
       * Can target and nested children for specified Selector (attribute)
       * whose value may be an expression that evaluates to True/False.
       */
      function scanForFocusable(target, selector) {
        var elFound, items = target[0].querySelectorAll(selector);

        // Find the last child element with the focus attribute
        if (items && items.length){
          items.length && angular.forEach(items, function(it) {
            it = angular.element(it);

            // Check the element for the md-autofocus class to ensure any associated expression
            // evaluated to true.
            var isFocusable = it.hasClass('md-autofocus');
            if (isFocusable) elFound = it;
          });
        }
        return elFound;
      }
    },

    /**
     * Disables scroll around the passed parent element.
     * @param {Element|angular.JQLite=} element Origin Element (not used)
     * @param {Element|angular.JQLite=} parent Element to disable scrolling within.
     *   Defaults to body if none supplied.
     * @param {Object=} options Object of options to modify functionality
     *   - disableScrollMask Boolean of whether or not to create a scroll mask element or
     *     use the passed parent element.
     */
    disableScrollAround: function(element, parent, options) {
      options = options || {};

      $mdUtil.disableScrollAround._count = Math.max(0, $mdUtil.disableScrollAround._count || 0);
      $mdUtil.disableScrollAround._count++;

      if ($mdUtil.disableScrollAround._restoreScroll) {
        return $mdUtil.disableScrollAround._restoreScroll;
      }

      var body = $document[0].body;
      var restoreBody = disableBodyScroll();
      var restoreElement = disableElementScroll(parent, options);

      return $mdUtil.disableScrollAround._restoreScroll = function() {
        if (--$mdUtil.disableScrollAround._count <= 0) {
          delete $mdUtil.disableScrollAround._viewPortTop;
          restoreBody();
          restoreElement();
          delete $mdUtil.disableScrollAround._restoreScroll;
        }
      };

      /**
       * Creates a virtual scrolling mask to prevent touchmove, keyboard, scrollbar clicking,
       * and wheel events.
       * @param {!Element|!angular.JQLite} elementToDisable
       * @param {Object=} scrollMaskOptions Object of options to modify functionality
       *   - disableScrollMask Boolean of whether or not to create a scroll mask element or
       *     use the passed parent element.
       * @returns {Function}
       */
      function disableElementScroll(elementToDisable, scrollMaskOptions) {
        var scrollMask;
        var wrappedElementToDisable = angular.element(elementToDisable || body);

        if (scrollMaskOptions.disableScrollMask) {
          scrollMask = wrappedElementToDisable;
        } else {
          scrollMask = angular.element(
            '<div class="md-scroll-mask">' +
            '  <div class="md-scroll-mask-bar"></div>' +
            '</div>');
          wrappedElementToDisable.append(scrollMask);
        }

        /**
         * @param {Event} $event
         */
        function preventDefault($event) {
          $event.preventDefault();
        }

        scrollMask.on('wheel touchmove', preventDefault);

        return function restoreElementScroll() {
          scrollMask.off('wheel touchmove', preventDefault);

          if (!scrollMaskOptions.disableScrollMask && scrollMask[0].parentNode) {
            scrollMask[0].parentNode.removeChild(scrollMask[0]);
          }
        };
      }

      // Converts the body to a position fixed block and translate it to the proper scroll position
      function disableBodyScroll() {
        var documentElement = $document[0].documentElement;

        var prevDocumentStyle = documentElement.style.cssText || '';
        var prevBodyStyle = body.style.cssText || '';

        var viewportTop = $mdUtil.getViewportTop();
        $mdUtil.disableScrollAround._viewPortTop = viewportTop;
        var clientWidth = body.clientWidth;
        var hasVerticalScrollbar = body.scrollHeight > body.clientHeight + 1;

        // Scroll may be set on <html> element (for example by overflow-y: scroll)
        // but Chrome is reporting the scrollTop position always on <body>.
        // scrollElement will allow to restore the scrollTop position to proper target.
        var scrollElement = documentElement.scrollTop > 0 ? documentElement : body;

        if (hasVerticalScrollbar) {
          angular.element(body).css({
            position: 'fixed',
            width: '100%',
            top: -viewportTop + 'px'
          });
        }

        if (body.clientWidth < clientWidth) {
          body.style.overflow = 'hidden';
        }

        return function restoreScroll() {
          // Reset the inline style CSS to the previous.
          body.style.cssText = prevBodyStyle;
          documentElement.style.cssText = prevDocumentStyle;

          // The scroll position while being fixed
          scrollElement.scrollTop = viewportTop;
        };
      }

    },

    enableScrolling: function() {
      var restoreFn = this.disableScrollAround._restoreScroll;
      restoreFn && restoreFn();
    },

    floatingScrollbars: function() {
      if (this.floatingScrollbars.cached === undefined) {
        var tempNode = angular.element('<div><div></div></div>').css({
          width: '100%',
          'z-index': -1,
          position: 'absolute',
          height: '35px',
          'overflow-y': 'scroll'
        });
        tempNode.children().css('height', '60px');

        $document[0].body.appendChild(tempNode[0]);
        this.floatingScrollbars.cached = (tempNode[0].offsetWidth == tempNode[0].childNodes[0].offsetWidth);
        tempNode.remove();
      }
      return this.floatingScrollbars.cached;
    },

    /**
     * Mobile safari only allows you to set focus in click event listeners.
     * @param {Element|angular.JQLite} element to focus
     */
    forceFocus: function(element) {
      var node = element[0] || element;

      document.addEventListener('click', function focusOnClick(ev) {
        if (ev.target === node && ev.$focus) {
          node.focus();
          ev.stopImmediatePropagation();
          ev.preventDefault();
          node.removeEventListener('click', focusOnClick);
        }
      }, true);

      var newEvent = document.createEvent('MouseEvents');
      newEvent.initMouseEvent('click', false, true, window, {}, 0, 0, 0, 0,
        false, false, false, false, 0, null);
      newEvent.$material = true;
      newEvent.$focus = true;
      node.dispatchEvent(newEvent);
    },

    /**
     * facade to build md-backdrop element with desired styles
     * NOTE: Use $compile to trigger backdrop postLink function
     */
    createBackdrop: function(scope, addClass) {
      return $compile($mdUtil.supplant('<md-backdrop class="{0}">', [addClass]))(scope);
    },

    /**
     * supplant() method from Crockford's `Remedial Javascript`
     * Equivalent to use of $interpolate; without dependency on
     * interpolation symbols and scope. Note: the '{<token>}' can
     * be property names, property chains, or array indices.
     */
    supplant: function(template, values, pattern) {
      pattern = pattern || /\{([^{}]*)\}/g;
      return template.replace(pattern, function(a, b) {
        var p = b.split('.'),
          r = values;
        try {
          for (var s in p) {
            if (p.hasOwnProperty(s)) {
              r = r[p[s]];
            }
          }
        } catch (e) {
          r = a;
        }
        return (typeof r === 'string' || typeof r === 'number') ? r : a;
      });
    },

    fakeNgModel: function() {
      return {
        $fake: true,
        $setTouched: angular.noop,
        $setViewValue: function(value) {
          this.$viewValue = value;
          this.$render(value);
          this.$viewChangeListeners.forEach(function(cb) {
            cb();
          });
        },
        $isEmpty: function(value) {
          return ('' + value).length === 0;
        },
        $parsers: [],
        $formatters: [],
        $viewChangeListeners: [],
        $render: angular.noop
      };
    },

    /**
     * @param {Function} func original function to be debounced
     * @param {number} wait number of milliseconds to delay (since last debounce reset).
     *  Default value 10 msecs.
     * @param {Object} scope in which to apply the function after debouncing ends
     * @param {boolean} invokeApply should the $timeout trigger $digest() dirty checking
     * @return {Function} A function, that, as long as it continues to be invoked, will not be
     *  triggered. The function will be called after it stops being called for N milliseconds.
     */
    debounce: function(func, wait, scope, invokeApply) {
      var timer;

      return function debounced() {
        var context = scope,
            args = Array.prototype.slice.call(arguments);

        $timeout.cancel(timer);
        timer = $timeout(function() {

          timer = undefined;
          func.apply(context, args);

        }, wait || 10, invokeApply);
      };
    },

    /**
     * The function will not be called unless it has been more than `delay` milliseconds since the
     * last call.
     * @param {Function} func original function to throttle
     * @param {number} delay number of milliseconds to delay
     * @return {Function} a function that can only be triggered every `delay` milliseconds.
     */
    throttle: function throttle(func, delay) {
      var recent;
      return function throttled() {
        var context = this;
        var args = arguments;
        var now = $mdUtil.now();

        if (!recent || (now - recent > delay)) {
          func.apply(context, args);
          recent = now;
        }
      };
    },

    /**
     * Measures the number of milliseconds taken to run the provided callback
     * function. Uses a high-precision timer if available.
     */
    time: function time(cb) {
      var start = $mdUtil.now();
      cb();
      return $mdUtil.now() - start;
    },

    /**
     * Create an implicit getter that caches its `getter()`
     * lookup value
     */
    valueOnUse : function (scope, key, getter) {
      var value = null, args = Array.prototype.slice.call(arguments);
      var params = (args.length > 3) ? args.slice(3) : [];

      Object.defineProperty(scope, key, {
        get: function () {
          if (value === null) value = getter.apply(scope, params);
          return value;
        }
      });
    },

    /**
     * Get a unique ID.
     *
     * @returns {string} an unique numeric string
     */
    nextUid: function() {
      return '' + nextUniqueId++;
    },

    /**
     * Stop watchers and events from firing on a scope without destroying it,
     * by disconnecting it from its parent and its siblings' linked lists.
     * @param {Object} scope to disconnect
     */
    disconnectScope: function disconnectScope(scope) {
      if (!scope) return;

      // we can't destroy the root scope or a scope that has been already destroyed
      if (scope.$root === scope) return;
      if (scope.$$destroyed) return;

      var parent = scope.$parent;
      scope.$$disconnected = true;

      // See Scope.$destroy
      if (parent.$$childHead === scope) parent.$$childHead = scope.$$nextSibling;
      if (parent.$$childTail === scope) parent.$$childTail = scope.$$prevSibling;
      if (scope.$$prevSibling) scope.$$prevSibling.$$nextSibling = scope.$$nextSibling;
      if (scope.$$nextSibling) scope.$$nextSibling.$$prevSibling = scope.$$prevSibling;

      scope.$$nextSibling = scope.$$prevSibling = null;

    },

    /**
     * Undo the effects of disconnectScope().
     * @param {Object} scope to reconnect
     */
    reconnectScope: function reconnectScope(scope) {
      if (!scope) return;

      // we can't disconnect the root node or scope already disconnected
      if (scope.$root === scope) return;
      if (!scope.$$disconnected) return;

      var child = scope;

      var parent = child.$parent;
      child.$$disconnected = false;
      // See Scope.$new for this logic...
      child.$$prevSibling = parent.$$childTail;
      if (parent.$$childHead) {
        parent.$$childTail.$$nextSibling = child;
        parent.$$childTail = child;
      } else {
        parent.$$childHead = parent.$$childTail = child;
      }
    },

    /**
     * Get an element's siblings matching a given tag name.
     *
     * @param {JQLite|angular.element|HTMLElement} element Element to start walking the DOM from
     * @param {string} tagName HTML tag name to match against
     * @returns {Object[]} JQLite
     */
    getSiblings: function getSiblings(element, tagName) {
      var upperCasedTagName = tagName.toUpperCase();
      if (element instanceof angular.element) {
        element = element[0];
      }
      var siblings = Array.prototype.filter.call(element.parentNode.children, function(node) {
        return element !== node && node.tagName.toUpperCase() === upperCasedTagName;
      });
      return siblings.map(function (sibling) {
        return angular.element(sibling);
      });
    },

    /*
     * getClosest replicates jQuery.closest() to walk up the DOM tree until it finds a matching nodeName
     *
     * @param {Node} el Element to start walking the DOM from
     * @param {string|function} validateWith If a string is passed, it will be evaluated against
     * each of the parent nodes' tag name. If a function is passed, the loop will call it with each
     * of the parents and will use the return value to determine whether the node is a match.
     * @param {boolean=} onlyParent Only start checking from the parent element, not `el`.
     * @returns {Node|null} closest matching parent Node or null if not found
     */
    getClosest: function getClosest(el, validateWith, onlyParent) {
      if (angular.isString(validateWith)) {
        var tagName = validateWith.toUpperCase();
        validateWith = function(el) {
          return el.nodeName.toUpperCase() === tagName;
        };
      }

      if (el instanceof angular.element) el = el[0];
      if (onlyParent) el = el.parentNode;
      if (!el) return null;

      do {
        if (validateWith(el)) {
          return el;
        }
      } while (el = el.parentNode);

      return null;
    },

    /**
     * Build polyfill for the Node.contains feature (if needed)
     * @param {Node} node
     * @param {Node} child
     * @returns {Node}
     */
    elementContains: function(node, child) {
      var hasContains = (window.Node && window.Node.prototype && Node.prototype.contains);
      var findFn = hasContains ? angular.bind(node, node.contains) : angular.bind(node, function(arg) {
        // compares the positions of two nodes and returns a bitmask
        return (node === child) || !!(this.compareDocumentPosition(arg) & 16);
      });

      return findFn(child);
    },

    /**
     * Functional equivalent for $element.filter(‘md-bottom-sheet’)
     * useful with interimElements where the element and its container are important...
     *
     * @param {angular.JQLite} element to scan
     * @param {string} nodeName of node to find (e.g. 'md-dialog')
     * @param {boolean=} scanDeep optional flag to allow deep scans; defaults to 'false'.
     * @param {boolean=} warnNotFound optional flag to enable log warnings; defaults to false
     */
    extractElementByName: function(element, nodeName, scanDeep, warnNotFound) {
      var found = scanTree(element);
      if (!found && !!warnNotFound) {
        $log.warn($mdUtil.supplant("Unable to find node '{0}' in element '{1}'.",[nodeName, element[0].outerHTML]));
      }

      return angular.element(found || element);

      /**
       * Breadth-First tree scan for element with matching `nodeName`
       */
      function scanTree(element) {
        return scanLevel(element) || (scanDeep ? scanChildren(element) : null);
      }

      /**
       * Case-insensitive scan of current elements only (do not descend).
       */
      function scanLevel(element) {
        if (element) {
          for (var i = 0, len = element.length; i < len; i++) {
            if (element[i].nodeName.toLowerCase() === nodeName) {
              return element[i];
            }
          }
        }
        return null;
      }

      /**
       * Scan children of specified node
       */
      function scanChildren(element) {
        var found;
        if (element) {
          for (var i = 0, len = element.length; i < len; i++) {
            var target = element[i];
            if (!found) {
              for (var j = 0, numChild = target.childNodes.length; j < numChild; j++) {
                found = found || scanTree([target.childNodes[j]]);
              }
            }
          }
        }
        return found;
      }

    },

    /**
     * Give optional properties with no value a boolean true if attr provided or false otherwise
     */
    initOptionalProperties: function(scope, attr, defaults) {
      defaults = defaults || {};
      angular.forEach(scope.$$isolateBindings, function(binding, key) {
        if (binding.optional && angular.isUndefined(scope[key])) {
          var attrIsDefined = angular.isDefined(attr[binding.attrName]);
          scope[key] = angular.isDefined(defaults[key]) ? defaults[key] : attrIsDefined;
        }
      });
    },

    /**
     * Alternative to $timeout calls with 0 delay.
     * nextTick() coalesces all calls within a single frame
     * to minimize $digest thrashing
     *
     * @param {Function} callback function to be called after the tick
     * @param {boolean=} digest true to call $rootScope.$digest() after callback
     * @param {Object=} scope associated with callback. If the scope is destroyed, the callback will
     *  be skipped.
     * @returns {*}
     */
    nextTick: function(callback, digest, scope) {
      // grab function reference for storing state details
      var nextTick = $mdUtil.nextTick;
      var timeout = nextTick.timeout;
      var queue = nextTick.queue || [];

      // add callback to the queue
      queue.push({scope: scope, callback: callback});

      // set default value for digest
      if (digest == null) digest = true;

      // store updated digest/queue values
      nextTick.digest = nextTick.digest || digest;
      nextTick.queue = queue;

      // either return existing timeout or create a new one
      return timeout || (nextTick.timeout = $timeout(processQueue, 0, false));

      /**
       * Grab a copy of the current queue
       * Clear the queue for future use
       * Process the existing queue
       * Trigger digest if necessary
       */
      function processQueue() {
        var queue = nextTick.queue;
        var digest = nextTick.digest;

        nextTick.queue = [];
        nextTick.timeout = null;
        nextTick.digest = false;

        queue.forEach(function(queueItem) {
          var skip = queueItem.scope && queueItem.scope.$$destroyed;
          if (!skip) {
            queueItem.callback();
          }
        });

        if (digest) $rootScope.$digest();
      }
    },

    /**
     * Processes a template and replaces the start/end symbols if the application has
     * overridden them.
     *
     * @param template The template to process whose start/end tags may be replaced.
     * @returns {*}
     */
    processTemplate: function(template) {
      if (usesStandardSymbols) {
        return template;
      } else {
        if (!template || !angular.isString(template)) return template;
        return template.replace(/\{\{/g, startSymbol).replace(/}}/g, endSymbol);
      }
    },

    /**
     * Scan up dom hierarchy for enabled parent;
     */
    getParentWithPointerEvents: function (element) {
      var parent = element.parent();

      // jqLite might return a non-null, but still empty, parent; so check for parent and length
      while (hasComputedStyle(parent, 'pointer-events', 'none')) {
        parent = parent.parent();
      }

      return parent;
    },

    getNearestContentElement: function (element) {
      var current = element.parent()[0];
      // Look for the nearest parent md-content, stopping at the rootElement.
      while (current && current !== $rootElement[0] && current !== document.body && current.nodeName.toUpperCase() !== 'MD-CONTENT') {
        current = current.parentNode;
      }
      return current;
    },

    /**
     * Checks if the current browser is natively supporting the `sticky` position.
     * @returns {string} supported sticky property name
     */
    checkStickySupport: function() {
      var stickyProp;
      var testEl = angular.element('<div>');
      $document[0].body.appendChild(testEl[0]);

      var stickyProps = ['sticky', '-webkit-sticky'];
      for (var i = 0; i < stickyProps.length; ++i) {
        testEl.css({
          position: stickyProps[i],
          top: 0,
          'z-index': 2
        });

        if (testEl.css('position') == stickyProps[i]) {
          stickyProp = stickyProps[i];
          break;
        }
      }

      testEl.remove();

      return stickyProp;
    },

    /**
     * Parses an attribute value, mostly a string.
     * By default checks for negated values and returns `false´ if present.
     * Negated values are: (native falsy) and negative strings like:
     * `false` or `0`.
     * @param value Attribute value which should be parsed.
     * @param negatedCheck When set to false, won't check for negated values.
     * @returns {boolean}
     */
    parseAttributeBoolean: function(value, negatedCheck) {
      return value === '' || !!value && (negatedCheck === false || value !== 'false' && value !== '0');
    },

    hasComputedStyle: hasComputedStyle,

    /**
     * Returns true if the parent form of the element has been submitted.
     * @param element An AngularJS or HTML5 element.
     * @returns {boolean}
     */
    isParentFormSubmitted: function(element) {
      var parent = $mdUtil.getClosest(element, 'form');
      var form = parent ? angular.element(parent).controller('form') : null;

      return form ? form.$submitted : false;
    },

    /**
     * Animate the requested element's scrollTop to the requested scrollPosition with basic easing.
     * @param {!Element} element The element to scroll.
     * @param {number} scrollEnd The new/final scroll position.
     * @param {number=} duration Duration of the scroll. Default is 1000ms.
     */
    animateScrollTo: function(element, scrollEnd, duration) {
      var scrollStart = element.scrollTop;
      var scrollChange = scrollEnd - scrollStart;
      var scrollingDown = scrollStart < scrollEnd;
      var startTime = $mdUtil.now();

      $$rAF(scrollChunk);

      function scrollChunk() {
        var newPosition = calculateNewPosition();

        element.scrollTop = newPosition;

        if (scrollingDown ? newPosition < scrollEnd : newPosition > scrollEnd) {
          $$rAF(scrollChunk);
        }
      }

      function calculateNewPosition() {
        var easeDuration = duration || 1000;
        var currentTime = $mdUtil.now() - startTime;

        return ease(currentTime, scrollStart, scrollChange, easeDuration);
      }

      function ease(currentTime, start, change, duration) {
        // If the duration has passed (which can occur if our app loses focus due to $$rAF), jump
        // straight to the proper position
        if (currentTime > duration) {
          return start + change;
        }

        var ts = (currentTime /= duration) * currentTime;
        var tc = ts * currentTime;

        return start + change * (-2 * tc + 3 * ts);
      }
    },

    /**
     * Provides an easy mechanism for removing duplicates from an array.
     *
     *    var myArray = [1, 2, 2, 3, 3, 3, 4, 4, 4, 4];
     *
     *    $mdUtil.uniq(myArray) => [1, 2, 3, 4]
     *
     * @param {Array} array The array whose unique values should be returned.
     * @returns {Array|void} A copy of the array containing only unique values.
     */
    uniq: function(array) {
      if (!array) { return; }

      return array.filter(function(value, index, self) {
        return self.indexOf(value) === index;
      });
    },

    /**
     * Gets the inner HTML content of the given HTMLElement.
     * Only intended for use with SVG or Symbol elements in IE11.
     * @param {Element} element
     * @returns {string} the inner HTML of the element passed in
     */
    getInnerHTML: function(element) {
      // For SVG or Symbol elements, innerHTML returns `undefined` in IE.
      // Reference: https://stackoverflow.com/q/28129956/633107
      // The XMLSerializer API is supported on IE11 and is the recommended workaround.
      var serializer = new XMLSerializer();

      return Array.prototype.map.call(element.childNodes, function (child) {
        return serializer.serializeToString(child);
      }).join('');
    },

    /**
     * Gets the outer HTML content of the given HTMLElement.
     * Only intended for use with SVG or Symbol elements in IE11.
     * @param {Element} element
     * @returns {string} the outer HTML of the element passed in
     */
    getOuterHTML: function(element) {
      // For SVG or Symbol elements, outerHTML returns `undefined` in IE.
      // Reference: https://stackoverflow.com/q/29888050/633107
      // The XMLSerializer API is supported on IE11 and is the recommended workaround.
      var serializer = new XMLSerializer();
      return serializer.serializeToString(element);
    },

    /**
     * Support: IE 9-11 only
     * documentMode is an IE-only property
     * http://msdn.microsoft.com/en-us/library/ie/cc196988(v=vs.85).aspx
     */
    msie: window.document.documentMode,

    getTouchAction: function() {
      var testEl = document.createElement('div');
      var vendorPrefixes = ['', 'webkit', 'Moz', 'MS', 'ms', 'o'];

      for (var i = 0; i < vendorPrefixes.length; i++) {
        var prefix = vendorPrefixes[i];
        var property = prefix ? prefix + 'TouchAction' : 'touchAction';
        if (angular.isDefined(testEl.style[property])) {
          return property;
        }
      }
    },

    /**
     * @param {Event} event the event to calculate the bubble path for
     * @return {EventTarget[]} the set of nodes that this event could bubble up to
     */
    getEventPath: function(event) {
      var path = [];
      var currentTarget = event.target;
      while (currentTarget) {
        path.push(currentTarget);
        currentTarget = currentTarget.parentElement;
      }
      if (path.indexOf(window) === -1 && path.indexOf(document) === -1)
        path.push(document);
      if (path.indexOf(window) === -1)
        path.push(window);
      return path;
    },

    /**
     * Gets the string the user has entered and removes Regex identifiers
     * @param {string} term
     * @returns {string} sanitized string
     */
    sanitize: function(term) {
      if (!term) return term;
      return term.replace(/[\\^$*+?.()|{}[]/g, '\\$&');
    }
  };

  // Instantiate other namespace utility methods

  $mdUtil.dom.animator = $$mdAnimate($mdUtil);

  return $mdUtil;

  function getNode(el) {
    return el[0] || el;
  }
}

/**
 * Since removing jQuery from the demos, some code that uses `element.focus()` is broken.
 * We need to add `element.focus()`, because it's testable unlike `element[0].focus`.
 */
angular.element.prototype.focus = angular.element.prototype.focus || function() {
  if (this.length) {
    this[0].focus();
  }
  return this;
};

angular.element.prototype.blur = angular.element.prototype.blur || function() {
  if (this.length) {
    this[0].blur();
  }
  return this;
};

})();
(function(){
"use strict";

// Polyfill angular < 1.4 (provide $animateCss)
angular
  .module('material.core')
  .factory('$$mdAnimate', ["$q", "$timeout", "$mdConstant", "$animateCss", function($q, $timeout, $mdConstant, $animateCss){

     // Since $$mdAnimate is injected into $mdUtil... use a wrapper function
     // to subsequently inject $mdUtil as an argument to the AnimateDomUtils

     return function($mdUtil) {
       return AnimateDomUtils($mdUtil, $q, $timeout, $mdConstant, $animateCss);
     };
   }]);

/**
 * Factory function that requires special injections
 */
function AnimateDomUtils($mdUtil, $q, $timeout, $mdConstant, $animateCss) {
  var self;
  return self = {
    /**
     *
     */
    translate3d : function(target, from, to, options) {
      return $animateCss(target, {
        from: from,
        to: to,
        addClass: options.transitionInClass,
        removeClass: options.transitionOutClass,
        duration: options.duration
      })
      .start()
      .then(function() {
          // Resolve with reverser function...
          return reverseTranslate;
      });

      /**
       * Specific reversal of the request translate animation above...
       */
      function reverseTranslate (newFrom) {
        return $animateCss(target, {
           to: newFrom || from,
           addClass: options.transitionOutClass,
           removeClass: options.transitionInClass,
           duration: options.duration
        }).start();

      }
    },

    /**
     * Listen for transitionEnd event (with optional timeout)
     * Announce completion or failure via promise handlers
     */
    waitTransitionEnd: function (element, opts) {
      var TIMEOUT = 3000; // fallback is 3 secs

      return $q(function(resolve, reject){
        opts = opts || { };

        // If there is no transition is found, resolve immediately
        //
        // NOTE: using $mdUtil.nextTick() causes delays/issues
        if (noTransitionFound(opts.cachedTransitionStyles)) {
          TIMEOUT = 0;
        }

        var timer = $timeout(finished, opts.timeout || TIMEOUT);
        element.on($mdConstant.CSS.TRANSITIONEND, finished);

        /**
         * Upon timeout or transitionEnd, reject or resolve (respectively) this promise.
         * NOTE: Make sure this transitionEnd didn't bubble up from a child
         */
        function finished(ev) {
          if (ev && ev.target !== element[0]) return;

          if (ev) $timeout.cancel(timer);
          element.off($mdConstant.CSS.TRANSITIONEND, finished);

          // Never reject since ngAnimate may cause timeouts due missed transitionEnd events
          resolve();

        }

        /**
         * Checks whether or not there is a transition.
         *
         * @param styles The cached styles to use for the calculation. If null, getComputedStyle()
         * will be used.
         *
         * @returns {boolean} True if there is no transition/duration; false otherwise.
         */
        function noTransitionFound(styles) {
          styles = styles || window.getComputedStyle(element[0]);

          return styles.transitionDuration == '0s' || (!styles.transition && !styles.transitionProperty);
        }

      });
    },

    calculateTransformValues: function (element, originator) {
      var origin = originator.element;
      var bounds = originator.bounds;

      if (origin || bounds) {
        var originBnds = origin ? self.clientRect(origin) || currentBounds() : self.copyRect(bounds);
        var dialogRect = self.copyRect(element[0].getBoundingClientRect());
        var dialogCenterPt = self.centerPointFor(dialogRect);
        var originCenterPt = self.centerPointFor(originBnds);

        return {
          centerX: originCenterPt.x - dialogCenterPt.x,
          centerY: originCenterPt.y - dialogCenterPt.y,
          scaleX: Math.round(100 * Math.min(0.5, originBnds.width / dialogRect.width)) / 100,
          scaleY: Math.round(100 * Math.min(0.5, originBnds.height / dialogRect.height)) / 100
        };
      }
      return {centerX: 0, centerY: 0, scaleX: 0.5, scaleY: 0.5};

      /**
       * This is a fallback if the origin information is no longer valid, then the
       * origin bounds simply becomes the current bounds for the dialogContainer's parent
       */
      function currentBounds() {
        var cntr = element ? element.parent() : null;
        var parent = cntr ? cntr.parent() : null;

        return parent ? self.clientRect(parent) : null;
      }
    },

    /**
     * Calculate the zoom transform from dialog to origin.
     *
     * We use this to set the dialog position immediately;
     * then the md-transition-in actually translates back to
     * `translate3d(0,0,0) scale(1.0)`...
     *
     * NOTE: all values are rounded to the nearest integer
     */
    calculateZoomToOrigin: function (element, originator) {
      var zoomTemplate = "translate3d( {centerX}px, {centerY}px, 0 ) scale( {scaleX}, {scaleY} )";
      var buildZoom = angular.bind(null, $mdUtil.supplant, zoomTemplate);

      return buildZoom(self.calculateTransformValues(element, originator));
    },

    /**
     * Calculate the slide transform from panel to origin.
     * NOTE: all values are rounded to the nearest integer
     */
    calculateSlideToOrigin: function (element, originator) {
      var slideTemplate = "translate3d( {centerX}px, {centerY}px, 0 )";
      var buildSlide = angular.bind(null, $mdUtil.supplant, slideTemplate);

      return buildSlide(self.calculateTransformValues(element, originator));
    },

    /**
     * Enhance raw values to represent valid css stylings...
     */
    toCss : function(raw) {
      var css = { };
      var lookups = 'left top right bottom width height x y min-width min-height max-width max-height';

      angular.forEach(raw, function(value,key) {
        if (angular.isUndefined(value)) return;

        if (lookups.indexOf(key) >= 0) {
          css[key] = value + 'px';
        } else {
          switch (key) {
            case 'transition':
              convertToVendor(key, $mdConstant.CSS.TRANSITION, value);
              break;
            case 'transform':
              convertToVendor(key, $mdConstant.CSS.TRANSFORM, value);
              break;
            case 'transformOrigin':
              convertToVendor(key, $mdConstant.CSS.TRANSFORM_ORIGIN, value);
              break;
            case 'font-size':
              css['font-size'] = value; // font sizes aren't always in px
              break;
          }
        }
      });

      return css;

      function convertToVendor(key, vendor, value) {
        angular.forEach(vendor.split(' '), function (key) {
          css[key] = value;
        });
      }
    },

    /**
     * Convert the translate CSS value to key/value pair(s).
     * @param {string} transform
     * @param {boolean=} addTransition
     * @param {string=} transition
     * @return {Object} object containing CSS translate key/value pair(s)
     */
    toTransformCss: function (transform, addTransition, transition) {
      var css = {};
      angular.forEach($mdConstant.CSS.TRANSFORM.split(' '), function (key) {
        css[key] = transform;
      });

      if (addTransition) {
        transition = transition || "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1) !important";
        css.transition = transition;
      }

      return css;
    },

    /**
     *  Clone the Rect and calculate the height/width if needed
     */
    copyRect: function (source, destination) {
      if (!source) return null;

      destination = destination || {};

      angular.forEach('left top right bottom width height'.split(' '), function (key) {
        destination[key] = Math.round(source[key]);
      });

      destination.width = destination.width || (destination.right - destination.left);
      destination.height = destination.height || (destination.bottom - destination.top);

      return destination;
    },

    /**
     * Calculate ClientRect of element; return null if hidden or zero size
     */
    clientRect: function (element) {
      var bounds = angular.element(element)[0].getBoundingClientRect();
      var isPositiveSizeClientRect = function (rect) {
        return rect && (rect.width > 0) && (rect.height > 0);
      };

      // If the event origin element has zero size, it has probably been hidden.
      return isPositiveSizeClientRect(bounds) ? self.copyRect(bounds) : null;
    },

    /**
     *  Calculate 'rounded' center point of Rect
     */
    centerPointFor: function (targetRect) {
      return targetRect ? {
        x: Math.round(targetRect.left + (targetRect.width / 2)),
        y: Math.round(targetRect.top + (targetRect.height / 2))
      } : { x : 0, y : 0 };
    }

  };
}


})();
(function(){
"use strict";

if (angular.version.minor >= 4) {
  angular.module('material.core.animate', []);
} else {
(function() {
  "use strict";

  var forEach = angular.forEach;

  var WEBKIT = angular.isDefined(document.documentElement.style.WebkitAppearance);
  var TRANSITION_PROP = WEBKIT ? 'WebkitTransition' : 'transition';
  var ANIMATION_PROP = WEBKIT ? 'WebkitAnimation' : 'animation';
  var PREFIX = WEBKIT ? '-webkit-' : '';

  var TRANSITION_EVENTS = (WEBKIT ? 'webkitTransitionEnd ' : '') + 'transitionend';
  var ANIMATION_EVENTS = (WEBKIT ? 'webkitAnimationEnd ' : '') + 'animationend';

  var $$ForceReflowFactory = ['$document', function($document) {
    return function() {
      return $document[0].body.clientWidth + 1;
    };
  }];

  var $$rAFMutexFactory = ['$$rAF', function($$rAF) {
    return function() {
      var passed = false;
      $$rAF(function() {
        passed = true;
      });
      return function(fn) {
        passed ? fn() : $$rAF(fn);
      };
    };
  }];

  var $$AnimateRunnerFactory = ['$q', '$$rAFMutex', function($q, $$rAFMutex) {
    var INITIAL_STATE = 0;
    var DONE_PENDING_STATE = 1;
    var DONE_COMPLETE_STATE = 2;

    function AnimateRunner(host) {
      this.setHost(host);

      this._doneCallbacks = [];
      this._runInAnimationFrame = $$rAFMutex();
      this._state = 0;
    }

    AnimateRunner.prototype = {
      setHost: function(host) {
        this.host = host || {};
      },

      done: function(fn) {
        if (this._state === DONE_COMPLETE_STATE) {
          fn();
        } else {
          this._doneCallbacks.push(fn);
        }
      },

      progress: angular.noop,

      getPromise: function() {
        if (!this.promise) {
          var self = this;
          this.promise = $q(function(resolve, reject) {
            self.done(function(status) {
              status === false ? reject() : resolve();
            });
          });
        }
        return this.promise;
      },

      then: function(resolveHandler, rejectHandler) {
        return this.getPromise().then(resolveHandler, rejectHandler);
      },

      'catch': function(handler) {
        return this.getPromise()['catch'](handler);
      },

      'finally': function(handler) {
        return this.getPromise()['finally'](handler);
      },

      pause: function() {
        if (this.host.pause) {
          this.host.pause();
        }
      },

      resume: function() {
        if (this.host.resume) {
          this.host.resume();
        }
      },

      end: function() {
        if (this.host.end) {
          this.host.end();
        }
        this._resolve(true);
      },

      cancel: function() {
        if (this.host.cancel) {
          this.host.cancel();
        }
        this._resolve(false);
      },

      complete: function(response) {
        var self = this;
        if (self._state === INITIAL_STATE) {
          self._state = DONE_PENDING_STATE;
          self._runInAnimationFrame(function() {
            self._resolve(response);
          });
        }
      },

      _resolve: function(response) {
        if (this._state !== DONE_COMPLETE_STATE) {
          forEach(this._doneCallbacks, function(fn) {
            fn(response);
          });
          this._doneCallbacks.length = 0;
          this._state = DONE_COMPLETE_STATE;
        }
      }
    };

    // Polyfill AnimateRunner.all which is used by input animations
    AnimateRunner.all = function(runners, callback) {
      var count = 0;
      var status = true;
      forEach(runners, function(runner) {
        runner.done(onProgress);
      });

      function onProgress(response) {
        status = status && response;
        if (++count === runners.length) {
          callback(status);
        }
      }
    };

    return AnimateRunner;
  }];

  angular
    .module('material.core.animate', [])
    .factory('$$forceReflow', $$ForceReflowFactory)
    .factory('$$AnimateRunner', $$AnimateRunnerFactory)
    .factory('$$rAFMutex', $$rAFMutexFactory)
    .factory('$animateCss', ['$window', '$$rAF', '$$AnimateRunner', '$$forceReflow', '$$jqLite', '$timeout', '$animate',
                     function($window,   $$rAF,   $$AnimateRunner,   $$forceReflow,   $$jqLite,   $timeout, $animate) {

      function init(element, options) {

        var temporaryStyles = [];
        var node = getDomNode(element);
        var areAnimationsAllowed = node && $animate.enabled();

        var hasCompleteStyles = false;
        var hasCompleteClasses = false;

        if (areAnimationsAllowed) {
          if (options.transitionStyle) {
            temporaryStyles.push([PREFIX + 'transition', options.transitionStyle]);
          }

          if (options.keyframeStyle) {
            temporaryStyles.push([PREFIX + 'animation', options.keyframeStyle]);
          }

          if (options.delay) {
            temporaryStyles.push([PREFIX + 'transition-delay', options.delay + 's']);
          }

          if (options.duration) {
            temporaryStyles.push([PREFIX + 'transition-duration', options.duration + 's']);
          }

          hasCompleteStyles = options.keyframeStyle ||
              (options.to && (options.duration > 0 || options.transitionStyle));
          hasCompleteClasses = !!options.addClass || !!options.removeClass;

          blockTransition(element, true);
        }

        var hasCompleteAnimation = areAnimationsAllowed && (hasCompleteStyles || hasCompleteClasses);

        applyAnimationFromStyles(element, options);

        var animationClosed = false;
        var events, eventFn;

        return {
          close: $window.close,
          start: function() {
            var runner = new $$AnimateRunner();
            waitUntilQuiet(function() {
              blockTransition(element, false);
              if (!hasCompleteAnimation) {
                return close();
              }

              forEach(temporaryStyles, function(entry) {
                var key = entry[0];
                var value = entry[1];
                node.style[camelCase(key)] = value;
              });

              applyClasses(element, options);

              var timings = computeTimings(element);
              if (timings.duration === 0) {
                return close();
              }

              var moreStyles = [];

              if (options.easing) {
                if (timings.transitionDuration) {
                  moreStyles.push([PREFIX + 'transition-timing-function', options.easing]);
                }
                if (timings.animationDuration) {
                  moreStyles.push([PREFIX + 'animation-timing-function', options.easing]);
                }
              }

              if (options.delay && timings.animationDelay) {
                moreStyles.push([PREFIX + 'animation-delay', options.delay + 's']);
              }

              if (options.duration && timings.animationDuration) {
                moreStyles.push([PREFIX + 'animation-duration', options.duration + 's']);
              }

              forEach(moreStyles, function(entry) {
                var key = entry[0];
                var value = entry[1];
                node.style[camelCase(key)] = value;
                temporaryStyles.push(entry);
              });

              var maxDelay = timings.delay;
              var maxDelayTime = maxDelay * 1000;
              var maxDuration = timings.duration;
              var maxDurationTime = maxDuration * 1000;
              var startTime = Date.now();

              events = [];
              if (timings.transitionDuration) {
                events.push(TRANSITION_EVENTS);
              }
              if (timings.animationDuration) {
                events.push(ANIMATION_EVENTS);
              }
              events = events.join(' ');
              eventFn = function(event) {
                event.stopPropagation();
                var ev = event.originalEvent || event;
                var timeStamp = ev.timeStamp || Date.now();
                var elapsedTime = parseFloat(ev.elapsedTime.toFixed(3));
                if (Math.max(timeStamp - startTime, 0) >= maxDelayTime && elapsedTime >= maxDuration) {
                  close();
                }
              };
              element.on(events, eventFn);

              applyAnimationToStyles(element, options);

              $timeout(close, maxDelayTime + maxDurationTime * 1.5, false);
            });

            return runner;

            function close() {
              if (animationClosed) return;
              animationClosed = true;

              if (events && eventFn) {
                element.off(events, eventFn);
              }
              applyClasses(element, options);
              applyAnimationStyles(element, options);
              forEach(temporaryStyles, function(entry) {
                node.style[camelCase(entry[0])] = '';
              });
              runner.complete(true);
              return runner;
            }
          }
        };
      }

      function applyClasses(element, options) {
        if (options.addClass) {
          $$jqLite.addClass(element, options.addClass);
          options.addClass = null;
        }
        if (options.removeClass) {
          $$jqLite.removeClass(element, options.removeClass);
          options.removeClass = null;
        }
      }

      function computeTimings(element) {
        var node = getDomNode(element);
        var cs = $window.getComputedStyle(node);
        var tdr = parseMaxTime(cs[prop('transitionDuration')]);
        var adr = parseMaxTime(cs[prop('animationDuration')]);
        var tdy = parseMaxTime(cs[prop('transitionDelay')]);
        var ady = parseMaxTime(cs[prop('animationDelay')]);

        adr *= (parseInt(cs[prop('animationIterationCount')], 10) || 1);
        var duration = Math.max(adr, tdr);
        var delay = Math.max(ady, tdy);

        return {
          duration: duration,
          delay: delay,
          animationDuration: adr,
          transitionDuration: tdr,
          animationDelay: ady,
          transitionDelay: tdy
        };

        function prop(key) {
          return WEBKIT ? 'Webkit' + key.charAt(0).toUpperCase() + key.substr(1)
                        : key;
        }
      }

      function parseMaxTime(str) {
        var maxValue = 0;
        var values = (str || "").split(/\s*,\s*/);
        forEach(values, function(value) {
          // it's always safe to consider only second values and omit `ms` values since
          // getComputedStyle will always handle the conversion for us
          if (value.charAt(value.length - 1) == 's') {
            value = value.substring(0, value.length - 1);
          }
          value = parseFloat(value) || 0;
          maxValue = maxValue ? Math.max(value, maxValue) : value;
        });
        return maxValue;
      }

      var cancelLastRAFRequest;
      var rafWaitQueue = [];
      function waitUntilQuiet(callback) {
        if (cancelLastRAFRequest) {
          cancelLastRAFRequest(); // cancels the request
        }
        rafWaitQueue.push(callback);
        cancelLastRAFRequest = $$rAF(function() {
          cancelLastRAFRequest = null;

          // DO NOT REMOVE THIS LINE OR REFACTOR OUT THE `pageWidth` variable.
          // PLEASE EXAMINE THE `$$forceReflow` service to understand why.
          var pageWidth = $$forceReflow();

          // we use a for loop to ensure that if the queue is changed
          // during this looping then it will consider new requests
          for (var i = 0; i < rafWaitQueue.length; i++) {
            rafWaitQueue[i](pageWidth);
          }
          rafWaitQueue.length = 0;
        });
      }

      function applyAnimationStyles(element, options) {
        applyAnimationFromStyles(element, options);
        applyAnimationToStyles(element, options);
      }

      function applyAnimationFromStyles(element, options) {
        if (options.from) {
          element.css(options.from);
          options.from = null;
        }
      }

      function applyAnimationToStyles(element, options) {
        if (options.to) {
          element.css(options.to);
          options.to = null;
        }
      }

      function getDomNode(element) {
        for (var i = 0; i < element.length; i++) {
          if (element[i].nodeType === 1) return element[i];
        }
      }

      function blockTransition(element, bool) {
        var node = getDomNode(element);
        var key = camelCase(PREFIX + 'transition-delay');
        node.style[key] = bool ? '-9999s' : '';
      }

      return init;
    }]);

  /**
   * Older browsers [FF31] expect camelCase
   * property keys.
   * e.g.
   *  animation-duration --> animationDuration
   */
  function camelCase(str) {
    return str.replace(/-[a-z]/g, function(str) {
      return str.charAt(1).toUpperCase();
    });
  }

})();

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.aria
 * @description
 * Aria Expectations for AngularJS Material components.
 */
MdAriaService.$inject = ["$$rAF", "$log", "$window", "$interpolate"];
angular
  .module('material.core')
  .provider('$mdAria', MdAriaProvider);

/**
 * @ngdoc service
 * @name $mdAriaProvider
 * @module material.core.aria
 *
 * @description
 *
 * Modify options of the `$mdAria` service, which will be used by most of the AngularJS Material
 * components.
 *
 * You are able to disable `$mdAria` warnings, by using the following markup.
 *
 * <hljs lang="js">
 *   app.config(function($mdAriaProvider) {
 *     // Globally disables all ARIA warnings.
 *     $mdAriaProvider.disableWarnings();
 *   });
 * </hljs>
 *
 */
function MdAriaProvider() {

  var config = {
    /** Whether we should show ARIA warnings in the console if labels are missing on the element */
    showWarnings: true
  };

  return {
    disableWarnings: disableWarnings,
    $get: ["$$rAF", "$log", "$window", "$interpolate", function($$rAF, $log, $window, $interpolate) {
      return MdAriaService.apply(config, arguments);
    }]
  };

  /**
   * @ngdoc method
   * @name $mdAriaProvider#disableWarnings
   * @description Disables all ARIA warnings generated by AngularJS Material.
   */
  function disableWarnings() {
    config.showWarnings = false;
  }
}

/*
 * @ngInject
 */
function MdAriaService($$rAF, $log, $window, $interpolate) {

  // Load the showWarnings option from the current context and store it inside of a scope variable,
  // because the context will be probably lost in some function calls.
  var showWarnings = this.showWarnings;

  return {
    expect: expect,
    expectAsync: expectAsync,
    expectWithText: expectWithText,
    expectWithoutText: expectWithoutText,
    getText: getText,
    hasAriaLabel: hasAriaLabel,
    parentHasAriaLabel: parentHasAriaLabel
  };

  /**
   * Check if expected attribute has been specified on the target element or child
   * @param element
   * @param attrName
   * @param {optional} defaultValue What to set the attr to if no value is found
   */
  function expect(element, attrName, defaultValue) {

    var node = angular.element(element)[0] || element;

    // if node exists and neither it nor its children have the attribute
    if (node &&
       ((!node.hasAttribute(attrName) ||
        node.getAttribute(attrName).length === 0) &&
        !childHasAttribute(node, attrName))) {

      defaultValue = angular.isString(defaultValue) ? defaultValue.trim() : '';
      if (defaultValue.length) {
        element.attr(attrName, defaultValue);
      } else if (showWarnings) {
        $log.warn('ARIA: Attribute "', attrName, '", required for accessibility, is missing on node:', node);
      }

    }
  }

  function expectAsync(element, attrName, defaultValueGetter) {
    // Problem: when retrieving the element's contents synchronously to find the label,
    // the text may not be defined yet in the case of a binding.
    // There is a higher chance that a binding will be defined if we wait one frame.
    $$rAF(function() {
        expect(element, attrName, defaultValueGetter());
    });
  }

  function expectWithText(element, attrName) {
    var content = getText(element) || "";
    var hasBinding = content.indexOf($interpolate.startSymbol()) > -1;

    if (hasBinding) {
      expectAsync(element, attrName, function() {
        return getText(element);
      });
    } else {
      expect(element, attrName, content);
    }
  }

  function expectWithoutText(element, attrName) {
    var content = getText(element);
    var hasBinding = content.indexOf($interpolate.startSymbol()) > -1;

    if (!hasBinding && !content) {
      expect(element, attrName, content);
    }
  }

  function getText(element) {
    element = element[0] || element;
    var walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
    var text = '';

    var node;
    while (node = walker.nextNode()) {
      if (!isAriaHiddenNode(node)) {
        text += node.textContent;
      }
    }

    return text.trim() || '';

    function isAriaHiddenNode(node) {
      while (node.parentNode && (node = node.parentNode) !== element) {
        if (node.getAttribute && node.getAttribute('aria-hidden') === 'true') {
          return true;
        }
      }
    }
  }

  function childHasAttribute(node, attrName) {
    var hasChildren = node.hasChildNodes(),
        hasAttr = false;

    function isHidden(el) {
      var style = el.currentStyle ? el.currentStyle : $window.getComputedStyle(el);
      return (style.display === 'none');
    }

    if (hasChildren) {
      var children = node.childNodes;
      for (var i=0; i < children.length; i++) {
        var child = children[i];
        if (child.nodeType === 1 && child.hasAttribute(attrName)) {
          if (!isHidden(child)) {
            hasAttr = true;
          }
        }
      }
    }
    return hasAttr;
  }

  /**
   * Check if expected element has aria label attribute
   * @param element
   */
  function hasAriaLabel(element) {
    var node = angular.element(element)[0] || element;

    /* Check if compatible node type (ie: not HTML Document node) */
    if (!node.hasAttribute) {
      return false;
    }

    /* Check label or description attributes */
    return node.hasAttribute('aria-label') || node.hasAttribute('aria-labelledby') || node.hasAttribute('aria-describedby');
  }

  /**
   * Check if expected element's parent has aria label attribute and has valid role and tagName
   * @param element
   * @param {optional} level Number of levels deep search should be performed
   */
  function parentHasAriaLabel(element, level) {
    level = level || 1;
    var node = angular.element(element)[0] || element;
    if (!node.parentNode) {
      return false;
    }
    if (performCheck(node.parentNode)) {
      return true;
    }
    level--;
    if (level) {
      return parentHasAriaLabel(node.parentNode, level);
    }
    return false;

    function performCheck(parentNode) {
      if (!hasAriaLabel(parentNode)) {
        return false;
      }
      /* Perform role blacklist check */
      if (parentNode.hasAttribute('role')) {
        switch (parentNode.getAttribute('role').toLowerCase()) {
          case 'command':
          case 'definition':
          case 'directory':
          case 'grid':
          case 'list':
          case 'listitem':
          case 'log':
          case 'marquee':
          case 'menu':
          case 'menubar':
          case 'note':
          case 'presentation':
          case 'separator':
          case 'scrollbar':
          case 'status':
          case 'tablist':
            return false;
        }
      }
      /* Perform tagName blacklist check */
      switch (parentNode.tagName.toLowerCase()) {
        case 'abbr':
        case 'acronym':
        case 'address':
        case 'applet':
        case 'audio':
        case 'b':
        case 'bdi':
        case 'bdo':
        case 'big':
        case 'blockquote':
        case 'br':
        case 'canvas':
        case 'caption':
        case 'center':
        case 'cite':
        case 'code':
        case 'col':
        case 'data':
        case 'dd':
        case 'del':
        case 'dfn':
        case 'dir':
        case 'div':
        case 'dl':
        case 'em':
        case 'embed':
        case 'fieldset':
        case 'figcaption':
        case 'font':
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
        case 'hgroup':
        case 'html':
        case 'i':
        case 'ins':
        case 'isindex':
        case 'kbd':
        case 'keygen':
        case 'label':
        case 'legend':
        case 'li':
        case 'map':
        case 'mark':
        case 'menu':
        case 'object':
        case 'ol':
        case 'output':
        case 'pre':
        case 'presentation':
        case 'q':
        case 'rt':
        case 'ruby':
        case 'samp':
        case 'small':
        case 'source':
        case 'span':
        case 'status':
        case 'strike':
        case 'strong':
        case 'sub':
        case 'sup':
        case 'svg':
        case 'tbody':
        case 'td':
        case 'th':
        case 'thead':
        case 'time':
        case 'tr':
        case 'track':
        case 'tt':
        case 'ul':
        case 'var':
          return false;
      }
      return true;
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.compiler
 * @description
 * AngularJS Material template and element compiler.
 */
angular
  .module('material.core')
  .provider('$mdCompiler', MdCompilerProvider);

/**
 * @ngdoc service
 * @name $mdCompilerProvider
 * @module material.core.compiler
 * @description
 * The `$mdCompiler` is able to respect the AngularJS `$compileProvider.preAssignBindingsEnabled`
 * state when using AngularJS versions greater than or equal to 1.5.10 and less than 1.7.0.
 * See the [AngularJS documentation for `$compileProvider.preAssignBindingsEnabled`
 * ](https://code.angularjs.org/1.6.10/docs/api/ng/provider/$compileProvider#preAssignBindingsEnabled)
 * for more information.
 *
 * To enable/disable whether the controllers of dynamic AngularJS Material components
 * (i.e. dialog, panel, toast, bottomsheet) respect the AngularJS
 * `$compileProvider.preAssignBindingsEnabled` flag, call the AngularJS Material method:
 * `$mdCompilerProvider.respectPreAssignBindingsEnabled(boolean)`.
 *
 * This AngularJS Material *flag* doesn't affect directives/components created via regular
 * AngularJS methods. These constitute the majority of AngularJS Material and user-created
 * components. Only dynamic construction of elements such as Dialogs, Panels, Toasts, BottomSheets,
 * etc. may be affected. Invoking `$mdCompilerProvider.respectPreAssignBindingsEnabled(true)`
 * will effect **bindings** in controllers created by AngularJS Material's services like
 * `$mdDialog`, `$mdPanel`, `$mdToast`, or `$mdBottomSheet`.
 *
 * See
 * <a ng-href="#mdcompilerprovider-respectpreassignbindingsenabled-respected">
 *   $mdCompilerProvider.respectPreAssignBindingsEnabled
 * </a>
 * for the details of how the different versions and settings of AngularJS affect this behavior.
 *
 * @usage
 *
 * Respect the AngularJS Compiler Setting
 *
 * <hljs lang="js">
 *   app.config(function($mdCompilerProvider) {
 *     $mdCompilerProvider.respectPreAssignBindingsEnabled(true);
 *   });
 * </hljs>
 *
 * @example
 * Using the default (backwards compatible) values for AngularJS 1.6
 * - AngularJS' `$compileProvider.preAssignBindingsEnabled(false)`
 * - AngularJS Material's `$mdCompilerProvider.respectPreAssignBindingsEnabled(false)`
 * <br><br>
 *
 * <hljs lang="js">
 * $mdDialog.show({
 *   locals: {
 *     myVar: true
 *   },
 *   controller: MyController,
 *   bindToController: true
 * }
 *
 * function MyController() {
 *   // Locals from Angular Material are available. e.g myVar is true.
 * }
 *
 * MyController.prototype.$onInit = function() {
 *   // Bindings are also available in the $onInit lifecycle hook.
 * }
 * </hljs>
 *
 * Recommended Settings for AngularJS 1.6
 * - AngularJS' `$compileProvider.preAssignBindingsEnabled(false)`
 * - AngularJS Material's `$mdCompilerProvider.respectPreAssignBindingsEnabled(true)`
 * <br><br>
 *
 * <hljs lang="js">
 * $mdDialog.show({
 *   locals: {
 *     myVar: true
 *   },
 *   controller: MyController,
 *   bindToController: true
 * }
 *
 * function MyController() {
 *   // No locals from Angular Material are available. e.g myVar is undefined.
 * }
 *
 * MyController.prototype.$onInit = function() {
 *   // Bindings are now available in the $onInit lifecycle hook.
 * }
 * </hljs>
 *
 */
MdCompilerProvider.$inject = ['$compileProvider'];
function MdCompilerProvider($compileProvider) {

  var provider = this;

  /**
   * @ngdoc method
   * @name $mdCompilerProvider#respectPreAssignBindingsEnabled
   *
   * @param {boolean=} respected update the `respectPreAssignBindingsEnabled` state if provided,
   *  otherwise just return the current Material `respectPreAssignBindingsEnabled` state.
   * @returns {boolean|MdCompilerProvider} current value, if used as a getter, or itself (chaining)
   *  if used as a setter.
   *
   * @description
   * Call this method to enable/disable whether Material-specific (dialog/panel/toast/bottomsheet)
   * controllers respect the AngularJS `$compileProvider.preAssignBindingsEnabled` flag. Note that
   * this doesn't affect directives/components created via regular AngularJS methods which
   * constitute most Material and user-created components.
   *
   * If disabled (`false`), the compiler assigns the value of each of the bindings to the
   * properties of the controller object before the constructor of this object is called.
   * The ability to disable this settings is **deprecated** and will be removed in
   * AngularJS Material 1.2.0.
   *
   * If enabled (`true`) the behavior depends on the AngularJS version used:
   *
   * - `<1.5.10`
   *  - Bindings are pre-assigned.
   * - `>=1.5.10 <1.7`
   *  - Respects whatever `$compileProvider.preAssignBindingsEnabled()` reports. If the
   *    `preAssignBindingsEnabled` flag wasn't set manually, it defaults to pre-assigning bindings
   *    with AngularJS `1.5` and to calling the constructor first with AngularJS `1.6`.
   * - `>=1.7`
   *  - The compiler calls the constructor first before assigning bindings and
   *    `$compileProvider.preAssignBindingsEnabled()` no longer exists.
   *
   * Defaults
   * - The default value is `false` in AngularJS 1.6 and earlier.
   *  - It is planned to fix this value to `true` and not allow the `false` value in
   *    AngularJS Material 1.2.0.
   *
   * It is recommended to set this flag to `true` when using AngularJS Material 1.1.x with
   * AngularJS versions >= 1.5.10. The only reason it's not set that way by default is backwards
   * compatibility.
   *
   * By not setting the flag to `true` when AngularJS' `$compileProvider.preAssignBindingsEnabled()`
   * is set to `false` (i.e. default behavior in AngularJS 1.6 or newer), unit testing of
   * Material Dialog/Panel/Toast/BottomSheet controllers using the `$controller` helper
   * is problematic as it always follows AngularJS' `$compileProvider.preAssignBindingsEnabled()`
   * value.
   */
  var respectPreAssignBindingsEnabled = false;
  this.respectPreAssignBindingsEnabled = function(respected) {
    if (angular.isDefined(respected)) {
      respectPreAssignBindingsEnabled = respected;
      return this;
    }

    return respectPreAssignBindingsEnabled;
  };

  /**
   * @private
   * @description
   * This function returns `true` if AngularJS Material-specific (dialog/panel/toast/bottomsheet)
   * controllers have bindings pre-assigned in controller constructors and `false` otherwise.
   *
   * Note that this doesn't affect directives/components created via regular AngularJS methods
   * which constitute most Material and user-created components; their behavior can be checked via
   * `$compileProvider.preAssignBindingsEnabled()` in AngularJS `>=1.5.10 <1.7.0`.
   *
   * @returns {*} current preAssignBindingsEnabled state
   */
  function getPreAssignBindingsEnabled() {
    if (!respectPreAssignBindingsEnabled) {
      // respectPreAssignBindingsEnabled === false
      // We're ignoring the AngularJS `$compileProvider.preAssignBindingsEnabled()` value in this case.
      return true;
    }

    // respectPreAssignBindingsEnabled === true

    // This check is needed because $compileProvider.preAssignBindingsEnabled does not exist prior
    // to AngularJS 1.5.10, is deprecated in AngularJS 1.6.x, and removed in AngularJS 1.7.x.
    if (typeof $compileProvider.preAssignBindingsEnabled === 'function') {
      return $compileProvider.preAssignBindingsEnabled();
    }

    // Flag respected but not present => apply logic based on AngularJS version used.
    if (angular.version.major === 1 && angular.version.minor < 6) {
      // AngularJS <1.5.10
      return true;
    }

    // AngularJS >=1.7.0
    return false;
  }

  this.$get = ["$q", "$templateRequest", "$injector", "$compile", "$controller",
    function($q, $templateRequest, $injector, $compile, $controller) {
      return new MdCompilerService($q, $templateRequest, $injector, $compile, $controller);
    }];

  /**
   * @ngdoc service
   * @name $mdCompiler
   * @module material.core.compiler
   * @description
   * The $mdCompiler service is an abstraction of AngularJS's compiler, that allows developers
   * to easily compile an element with options like in a Directive Definition Object.
   *
   * > The compiler powers a lot of components inside of AngularJS Material.
   * > Like the `$mdPanel` or `$mdDialog`.
   *
   * @usage
   *
   * Basic Usage with a template
   *
   * <hljs lang="js">
   *   $mdCompiler.compile({
   *     templateUrl: 'modal.html',
   *     controller: 'ModalCtrl',
   *     locals: {
   *       modal: myModalInstance;
   *     }
   *   }).then(function (compileData) {
   *     compileData.element; // Compiled DOM element
   *     compileData.link(myScope); // Instantiate controller and link element to scope.
   *   });
   * </hljs>
   *
   * Example with a content element
   *
   * <hljs lang="js">
   *
   *   // Create a virtual element and link it manually.
   *   // The compiler doesn't need to recompile the element each time.
   *   var myElement = $compile('<span>Test</span>')(myScope);
   *
   *   $mdCompiler.compile({
   *     contentElement: myElement
   *   }).then(function (compileData) {
   *     compileData.element // Content Element (same as above)
   *     compileData.link // This does nothing when using a contentElement.
   *   });
   * </hljs>
   *
   * > Content Element is a significant performance improvement when the developer already knows that the
   * > compiled element will be always the same and the scope will not change either.
   *
   * The `contentElement` option also supports DOM elements which will be temporary removed and restored
   * at its old position.
   *
   * <hljs lang="js">
   *   var domElement = document.querySelector('#myElement');
   *
   *   $mdCompiler.compile({
   *     contentElement: myElement
   *   }).then(function (compileData) {
   *     compileData.element // Content Element (same as above)
   *     compileData.link // This does nothing when using a contentElement.
   *   });
   * </hljs>
   *
   * The `$mdCompiler` can also query for the element in the DOM itself.
   *
   * <hljs lang="js">
   *   $mdCompiler.compile({
   *     contentElement: '#myElement'
   *   }).then(function (compileData) {
   *     compileData.element // Content Element (same as above)
   *     compileData.link // This does nothing when using a contentElement.
   *   });
   * </hljs>
   *
   */
  function MdCompilerService($q, $templateRequest, $injector, $compile, $controller) {

    /**
     * @private @const
     * @type {!IQService}
     */
    this.$q = $q;

    /**
     * @private @const
     * @type {!ITemplateRequestService}
     */
    this.$templateRequest = $templateRequest;

    /**
     * @private @const
     * @type {!IInjectorService}
     */
    this.$injector = $injector;

    /**
     * @private @const
     * @type{!ICompileService}
     */
    this.$compile = $compile;

    /**
     * @private @const
     * @type {!IControllerService}
     */
    this.$controller = $controller;
  }

  /**
   * @ngdoc method
   * @name $mdCompiler#compile
   * @description
   *
   * A method to compile a HTML template with the AngularJS compiler.
   * The `$mdCompiler` is wrapper around the AngularJS compiler and provides extra functionality
   * like controller instantiation or async resolves.
   *
   * @param {!Object} options An options object, with the following properties:
   *
   *    - `controller` - `{string|function}` Controller fn that should be associated with
   *         newly created scope or the name of a registered controller if passed as a string.
   *    - `controllerAs` - `{string=}` A controller alias name. If present the controller will be
   *         published to scope under the `controllerAs` name.
   *    - `contentElement` - `{string|Element}`: Instead of using a template, which will be
   *         compiled each time, you can also use a DOM element.<br/>
   *    - `template` - `{string=}` An html template as a string.
   *    - `templateUrl` - `{string=}` A path to an html template.
   *    - `transformTemplate` - `{function(template)=}` A function which transforms the template after
   *        it is loaded. It will be given the template string as a parameter, and should
   *        return a a new string representing the transformed template.
   *    - `resolve` - `{Object.<string, function>=}` - An optional map of dependencies which should
   *        be injected into the controller. If any of these dependencies are promises, the compiler
   *        will wait for them all to be resolved, or if one is rejected before the controller is
   *        instantiated `compile()` will fail..
   *      * `key` - `{string}`: a name of a dependency to be injected into the controller.
   *      * `factory` - `{string|function}`: If `string` then it is an alias for a service.
   *        Otherwise if function, then it is injected and the return value is treated as the
   *        dependency. If the result is a promise, it is resolved before its value is
   *        injected into the controller.
   *
   * @returns {Object} promise A promise, which will be resolved with a `compileData` object.
   * `compileData` has the following properties:
   *
   *   - `element` - `{Element}`: an uncompiled element matching the provided template.
   *   - `link` - `{function(scope)}`: A link function, which, when called, will compile
   *     the element and instantiate the provided controller (if given).
   *   - `locals` - `{Object}`: The locals which will be passed into the controller once `link` is
   *     called. If `bindToController` is true, they will be copied to the ctrl instead
   */
  MdCompilerService.prototype.compile = function(options) {

    if (options.contentElement) {
      return this._prepareContentElement(options);
    } else {
      return this._compileTemplate(options);
    }

  };

  /**
   * Instead of compiling any template, the compiler just fetches an existing HTML element from the DOM and
   * provides a restore function to put the element back it old DOM position.
   * @param {!Object} options Options to be used for the compiler.
   */
  MdCompilerService.prototype._prepareContentElement = function(options) {

    var contentElement = this._fetchContentElement(options);

    return this.$q.resolve({
      element: contentElement.element,
      cleanup: contentElement.restore,
      locals: {},
      link: function() {
        return contentElement.element;
      }
    });

  };

  /**
   * Compiles a template by considering all options and waiting for all resolves to be ready.
   * @param {!Object} options Compile options
   * @returns {!Object} Compile data with link function.
   */
  MdCompilerService.prototype._compileTemplate = function(options) {

    var self = this;
    var templateUrl = options.templateUrl;
    var template = options.template || '';
    var resolve = angular.extend({}, options.resolve);
    var locals = angular.extend({}, options.locals);
    var transformTemplate = options.transformTemplate || angular.identity;

    // Take resolve values and invoke them.
    // Resolves can either be a string (value: 'MyRegisteredAngularConst'),
    // or an invokable 'factory' of sorts: (value: function ValueGetter($dependency) {})
    angular.forEach(resolve, function(value, key) {
      if (angular.isString(value)) {
        resolve[key] = self.$injector.get(value);
      } else {
        resolve[key] = self.$injector.invoke(value);
      }
    });

    // Add the locals, which are just straight values to inject
    // eg locals: { three: 3 }, will inject three into the controller
    angular.extend(resolve, locals);

    if (templateUrl) {
      resolve.$$ngTemplate = this.$templateRequest(templateUrl);
    } else {
      resolve.$$ngTemplate = this.$q.when(template);
    }


    // Wait for all the resolves to finish if they are promises
    return this.$q.all(resolve).then(function(locals) {

      var template = transformTemplate(locals.$$ngTemplate, options);
      var element = options.element || angular.element('<div>').html(template.trim()).contents();

      return self._compileElement(locals, element, options);
    });

  };

  /**
   * Method to compile an element with the given options.
   * @param {!Object} locals Locals to be injected to the controller if present
   * @param {!JQLite} element Element to be compiled and linked
   * @param {!Object} options Options to be used for linking.
   * @returns {!Object} Compile data with link function.
   */
  MdCompilerService.prototype._compileElement = function(locals, element, options) {
    var self = this;
    var ngLinkFn = this.$compile(element);

    var compileData = {
      element: element,
      cleanup: element.remove.bind(element),
      locals: locals,
      link: linkFn
    };

    function linkFn(scope) {
      locals.$scope = scope;

      // Instantiate controller if the developer provided one.
      if (options.controller) {

        var injectLocals = angular.extend({}, locals, {
          $element: element
        });

        // Create the specified controller instance.
        var ctrl = self._createController(options, injectLocals, locals);

        // Registering extra $destroy listeners should be avoided.
        // Only register the listener if the controller implements a $onDestroy hook.
        if (angular.isFunction(ctrl.$onDestroy)) {
          scope.$on('$destroy', function() {
            // Call the $onDestroy hook if it's present on the controller.
            angular.isFunction(ctrl.$onDestroy) && ctrl.$onDestroy();
          });
        }

        // Unique identifier for AngularJS Route ngView controllers.
        element.data('$ngControllerController', ctrl);
        element.children().data('$ngControllerController', ctrl);

        // Expose the instantiated controller to the compile data
        compileData.controller = ctrl;
      }

      // Invoke the AngularJS $compile link function.
      return ngLinkFn(scope);
    }

    return compileData;

  };

  /**
   * Creates and instantiates a new controller with the specified options.
   * @param {!Object} options Options that include the controller function or string.
   * @param {!Object} injectLocals Locals to to be provided in the controller DI.
   * @param {!Object} locals Locals to be injected to the controller.
   * @returns {!Object} Created controller instance.
   */
  MdCompilerService.prototype._createController = function(options, injectLocals, locals) {
    var ctrl;
    var preAssignBindingsEnabled = getPreAssignBindingsEnabled();
    // The third argument to $controller is considered private and undocumented:
    // https://github.com/angular/angular.js/blob/v1.6.10/src/ng/controller.js#L102-L109.
    // TODO remove the use of this third argument in AngularJS Material 1.2.0.
    // Passing `true` as the third argument causes `$controller` to return a function that
    // gets the controller instance instead of returning the instance directly. When the
    // controller is defined as a function, `invokeCtrl.instance` is the *same instance* as
    // `invokeCtrl()`. However, when the controller is an ES6 class, `invokeCtrl.instance` is a
    // *different instance* from `invokeCtrl()`.
    if (preAssignBindingsEnabled) {
      var invokeCtrl = this.$controller(options.controller, injectLocals, true);

      if (options.bindToController) {
        angular.extend(invokeCtrl.instance, locals);
      }

      // Use the private API callback to instantiate and initialize the specified controller.
      ctrl = invokeCtrl();
    } else {
      // If we don't need to pre-assign bindings, avoid using the private API third argument and
      // related callback.
      ctrl = this.$controller(options.controller, injectLocals);

      if (options.bindToController) {
        angular.extend(ctrl, locals);
      }
    }

    if (options.controllerAs) {
      injectLocals.$scope[options.controllerAs] = ctrl;
    }

    // Call the $onInit hook if it's present on the controller.
    angular.isFunction(ctrl.$onInit) && ctrl.$onInit();

    return ctrl;
  };

  /**
   * Fetches an element removing it from the DOM and using it temporary for the compiler.
   * Elements which were fetched will be restored after use.
   * @param {!Object} options Options to be used for the compilation.
   * @returns {{element: !JQLite, restore: !function}}
   */
  MdCompilerService.prototype._fetchContentElement = function(options) {
    var contentEl = options.contentElement;
    var restoreFn = null;

    if (angular.isString(contentEl)) {
      contentEl = document.querySelector(contentEl);
      restoreFn = createRestoreFn(contentEl);
    } else {
      contentEl = contentEl[0] || contentEl;

      // When the element is visible in the DOM, then we restore it at close of the dialog.
      // Otherwise it will be removed from the DOM after close.
      if (document.contains(contentEl)) {
        restoreFn = createRestoreFn(contentEl);
      } else {
        restoreFn = function() {
          if (contentEl.parentNode) {
            contentEl.parentNode.removeChild(contentEl);
          }
        };
      }
    }

    return {
      element: angular.element(contentEl),
      restore: restoreFn
    };

    function createRestoreFn(element) {
      var parent = element.parentNode;
      var nextSibling = element.nextElementSibling;

      return function() {
        if (!nextSibling) {
          // When the element didn't had any sibling, then it can be simply appended to the
          // parent, because it plays no role, which index it had before.
          parent.appendChild(element);
        } else {
          // When the element had a sibling, which marks the previous position of the element
          // in the DOM, we insert it correctly before the sibling, to have the same index as
          // before.
          parent.insertBefore(element, nextSibling);
        }
      };
    }
  };
}


})();
(function(){
"use strict";


MdGesture.$inject = ["$$MdGestureHandler", "$$rAF", "$timeout", "$mdUtil"];
attachToDocument.$inject = ["$mdGesture", "$$MdGestureHandler", "$mdUtil"];var HANDLERS = {};

/**
 * The state of the current 'pointer'. The pointer represents the state of the current touch.
 * It contains normalized x and y coordinates from DOM events,
 * as well as other information abstracted from the DOM.
 */
var pointer, lastPointer, maxClickDistance = 6;
var forceSkipClickHijack = false, disableAllGestures = false;

/**
 * The position of the most recent click if that click was on a label element.
 * @type {{x: number, y: number}|null}
 */
var lastLabelClickPos = null;

/**
 * Used to attach event listeners once when multiple ng-apps are running.
 * @type {boolean}
 */
var isInitialized = false;

/**
 * @ngdoc module
 * @name material.core.gestures
 * @description
 * AngularJS Material Gesture handling for touch devices.
 * This module replaced the usage of the HammerJS library.
 */
angular
  .module('material.core.gestures', [])
  .provider('$mdGesture', MdGestureProvider)
  .factory('$$MdGestureHandler', MdGestureHandler)
  .run(attachToDocument);

/**
 * @ngdoc service
 * @name $mdGestureProvider
 * @module material.core.gestures
 *
 * @description
 * In some scenarios on mobile devices (without jQuery), the click events should NOT be hijacked.
 * `$mdGestureProvider` is used to configure the Gesture module to ignore or skip click hijacking
 * on mobile devices.
 *
 * You can also change the max click distance, `6px` by default, if you have issues on some touch
 * screens.
 *
 * <hljs lang="js">
 *   app.config(function($mdGestureProvider) {
 *
 *     // For mobile devices without jQuery loaded, do not
 *     // intercept click events during the capture phase.
 *     $mdGestureProvider.skipClickHijack();
 *
 *     // If hijacking clicks, you may want to change the default click distance
 *     $mdGestureProvider.setMaxClickDistance(12);
 *   });
 * </hljs>
 *
 */
function MdGestureProvider() { }

MdGestureProvider.prototype = {

  /**
   * @ngdoc method
   * @name $mdGestureProvider#disableAll
   *
   * @description
   * Disable all gesture detection. This can be beneficial to application performance
   * and memory usage.
   */
  disableAll: function () {
    disableAllGestures = true;
  },

  // Publish access to setter to configure a variable BEFORE the
  // $mdGesture service is instantiated...
  /**
   * @ngdoc method
   * @name $mdGestureProvider#skipClickHijack
   *
   * @description
   * Tell the AngularJS Material Gesture module to skip (or ignore) click hijacking on mobile devices.
   */
  skipClickHijack: function() {
    return forceSkipClickHijack = true;
  },

  /**
   * @ngdoc method
   * @name $mdGestureProvider#setMaxClickDistance
   * @param clickDistance {string} Distance in pixels. I.e. `12px`.
   * @description
   * Set the max distance from the origin of the touch event to trigger touch handlers.
   */
  setMaxClickDistance: function(clickDistance) {
    maxClickDistance = parseInt(clickDistance);
  },

  /**
   * $get is used to build an instance of $mdGesture
   * @ngInject
   */
  $get : ["$$MdGestureHandler", "$$rAF", "$timeout", "$mdUtil", function($$MdGestureHandler, $$rAF, $timeout, $mdUtil) {
       return new MdGesture($$MdGestureHandler, $$rAF, $timeout, $mdUtil);
  }]
};



/**
 * MdGesture factory construction function
 * @ngInject
 */
function MdGesture($$MdGestureHandler, $$rAF, $timeout, $mdUtil) {
  var touchActionProperty = $mdUtil.getTouchAction();
  var hasJQuery = (typeof window.jQuery !== 'undefined') && (angular.element === window.jQuery);

  var self = {
    handler: addHandler,
    register: register,
    isAndroid: $mdUtil.isAndroid,
    isIos: $mdUtil.isIos,
    // On mobile w/out jQuery, we normally intercept clicks. Should we skip that?
    isHijackingClicks: ($mdUtil.isIos || $mdUtil.isAndroid) && !hasJQuery && !forceSkipClickHijack
  };

  if (self.isHijackingClicks) {
    self.handler('click', {
      options: {
        maxDistance: maxClickDistance
      },
      onEnd: checkDistanceAndEmit('click')
    });

    self.handler('focus', {
      options: {
        maxDistance: maxClickDistance
      },
      onEnd: function(ev, pointer) {
        if (pointer.distance < this.state.options.maxDistance && canFocus(ev.target)) {
          this.dispatchEvent(ev, 'focus', pointer);
          ev.target.focus();
        }
      }
    });

    self.handler('mouseup', {
      options: {
        maxDistance: maxClickDistance
      },
      onEnd: checkDistanceAndEmit('mouseup')
    });

    self.handler('mousedown', {
      onStart: function(ev) {
        this.dispatchEvent(ev, 'mousedown');
      }
    });
  }

  function checkDistanceAndEmit(eventName) {
    return function(ev, pointer) {
      if (pointer.distance < this.state.options.maxDistance) {
        this.dispatchEvent(ev, eventName, pointer);
      }
    };
  }

  /**
   * Register an element to listen for a handler.
   * This allows an element to override the default options for a handler.
   * Additionally, some handlers like drag and hold only dispatch events if
   * the domEvent happens inside an element that's registered to listen for these events.
   *
   * @see GestureHandler for how overriding of default options works.
   * @example $mdGesture.register(myElement, 'drag', { minDistance: 20, horizontal: false })
   */
  function register(element, handlerName, options) {
    var handler = HANDLERS[handlerName.replace(/^\$md./, '')];
    if (!handler) {
      throw new Error('Failed to register element with handler ' + handlerName + '. ' +
      'Available handlers: ' + Object.keys(HANDLERS).join(', '));
    }
    return handler.registerElement(element, options);
  }

  /*
   * add a handler to $mdGesture. see below.
   */
  function addHandler(name, definition) {
    var handler = new $$MdGestureHandler(name);
    angular.extend(handler, definition);
    HANDLERS[name] = handler;

    return self;
  }

  /**
   * Register handlers. These listen to touch/start/move events, interpret them,
   * and dispatch gesture events depending on options & conditions. These are all
   * instances of GestureHandler.
   * @see GestureHandler
   */
  return self
    /*
     * The press handler dispatches an event on touchdown/touchend.
     * It's a simple abstraction of touch/mouse/pointer start and end.
     */
    .handler('press', {
      onStart: function (ev, pointer) {
        this.dispatchEvent(ev, '$md.pressdown');
      },
      onEnd: function (ev, pointer) {
        this.dispatchEvent(ev, '$md.pressup');
      }
    })

    /*
     * The hold handler dispatches an event if the user keeps their finger within
     * the same <maxDistance> area for <delay> ms.
     * The hold handler will only run if a parent of the touch target is registered
     * to listen for hold events through $mdGesture.register()
     */
    .handler('hold', {
      options: {
        maxDistance: 6,
        delay: 500
      },
      onCancel: function () {
        $timeout.cancel(this.state.timeout);
      },
      onStart: function (ev, pointer) {
        // For hold, require a parent to be registered with $mdGesture.register()
        // Because we prevent scroll events, this is necessary.
        if (!this.state.registeredParent) return this.cancel();

        this.state.pos = {x: pointer.x, y: pointer.y};
        this.state.timeout = $timeout(angular.bind(this, function holdDelayFn() {
          this.dispatchEvent(ev, '$md.hold');
          this.cancel(); // we're done!
        }), this.state.options.delay, false);
      },
      onMove: function (ev, pointer) {
        // Don't scroll while waiting for hold.
        // If we don't preventDefault touchmove events here, Android will assume we don't
        // want to listen to anymore touch events. It will start scrolling and stop sending
        // touchmove events.
        if (!touchActionProperty && ev.type === 'touchmove') ev.preventDefault();

        // If the user moves greater than <maxDistance> pixels, stop the hold timer
        // set in onStart
        var dx = this.state.pos.x - pointer.x;
        var dy = this.state.pos.y - pointer.y;
        if (Math.sqrt(dx * dx + dy * dy) > this.options.maxDistance) {
          this.cancel();
        }
      },
      onEnd: function () {
        this.onCancel();
      }
    })

    /*
     * The drag handler dispatches a drag event if the user holds and moves his finger greater than
     * <minDistance> px in the x or y direction, depending on options.horizontal.
     * The drag will be cancelled if the user moves his finger greater than <minDistance>*<cancelMultiplier> in
     * the perpendicular direction. Eg if the drag is horizontal and the user moves his finger <minDistance>*<cancelMultiplier>
     * pixels vertically, this handler won't consider the move part of a drag.
     */
    .handler('drag', {
      options: {
        minDistance: 6,
        horizontal: true,
        cancelMultiplier: 1.5
      },
      /**
       * @param {angular.JQLite} element where touch action styles need to be adjusted
       * @param {{horizontal: boolean}=} options object whose horizontal property can specify to
       *  apply 'pan-y' or 'pan-x' touch actions.
       */
      onSetup: function(element, options) {
        if (touchActionProperty) {
          // We check for horizontal to be false, because otherwise we would overwrite the default opts.
          this.oldTouchAction = element[0].style[touchActionProperty];
          element[0].style[touchActionProperty] = options.horizontal ? 'pan-y' : 'pan-x';
        }
      },
      /**
       * @param {angular.JQLite} element where styles need to be cleaned up
       */
      onCleanup: function(element) {
        if (this.oldTouchAction) {
          element[0].style[touchActionProperty] = this.oldTouchAction;
        } else {
          element[0].style[touchActionProperty] = null;
        }
      },
      onStart: function (ev) {
        // For drag, require a parent to be registered with $mdGesture.register()
        if (!this.state.registeredParent) this.cancel();
      },
      onMove: function (ev, pointer) {
        var shouldStartDrag, shouldCancel;
        // Don't scroll while deciding if this touchmove qualifies as a drag event.
        // If we don't preventDefault touchmove events here, Android will assume we don't
        // want to listen to anymore touch events. It will start scrolling and stop sending
        // touchmove events.
        if (!touchActionProperty && ev.type === 'touchmove') ev.preventDefault();

        if (!this.state.dragPointer) {
          if (this.state.options.horizontal) {
            shouldStartDrag = Math.abs(pointer.distanceX) > this.state.options.minDistance;
            shouldCancel = Math.abs(pointer.distanceY) > this.state.options.minDistance * this.state.options.cancelMultiplier;
          } else {
            shouldStartDrag = Math.abs(pointer.distanceY) > this.state.options.minDistance;
            shouldCancel = Math.abs(pointer.distanceX) > this.state.options.minDistance * this.state.options.cancelMultiplier;
          }

          if (shouldStartDrag) {
            // Create a new pointer representing this drag, starting at this point where the drag started.
            this.state.dragPointer = makeStartPointer(ev);
            updatePointerState(ev, this.state.dragPointer);
            this.dispatchEvent(ev, '$md.dragstart', this.state.dragPointer);

          } else if (shouldCancel) {
            this.cancel();
          }
        } else {
          this.dispatchDragMove(ev);
        }
      },
      // Only dispatch dragmove events every frame; any more is unnecessary
      dispatchDragMove: $$rAF.throttle(function (ev) {
        // Make sure the drag didn't stop while waiting for the next frame
        if (this.state.isRunning) {
          updatePointerState(ev, this.state.dragPointer);
          this.dispatchEvent(ev, '$md.drag', this.state.dragPointer);
        }
      }),
      onEnd: function (ev, pointer) {
        if (this.state.dragPointer) {
          updatePointerState(ev, this.state.dragPointer);
          this.dispatchEvent(ev, '$md.dragend', this.state.dragPointer);
        }
      }
    })

    /*
     * The swipe handler will dispatch a swipe event if, on the end of a touch,
     * the velocity and distance were high enough.
     */
    .handler('swipe', {
      options: {
        minVelocity: 0.65,
        minDistance: 10
      },
      onEnd: function (ev, pointer) {
        var eventType;

        if (Math.abs(pointer.velocityX) > this.state.options.minVelocity &&
          Math.abs(pointer.distanceX) > this.state.options.minDistance) {
          eventType = pointer.directionX == 'left' ? '$md.swipeleft' : '$md.swiperight';
          this.dispatchEvent(ev, eventType);
        }
        else if (Math.abs(pointer.velocityY) > this.state.options.minVelocity &&
          Math.abs(pointer.distanceY) > this.state.options.minDistance) {
          eventType = pointer.directionY == 'up' ? '$md.swipeup' : '$md.swipedown';
          this.dispatchEvent(ev, eventType);
        }
      }
    });
}

/**
 * MdGestureHandler
 * A GestureHandler is an object which is able to dispatch custom dom events
 * based on native dom {touch,pointer,mouse}{start,move,end} events.
 *
 * A gesture will manage its lifecycle through the start,move,end, and cancel
 * functions, which are called by native dom events.
 *
 * A gesture has the concept of 'options' (eg. a swipe's required velocity), which can be
 * overridden by elements registering through $mdGesture.register().
 */
function GestureHandler (name) {
  this.name = name;
  this.state = {};
}

function MdGestureHandler() {
  var hasJQuery =  (typeof window.jQuery !== 'undefined') && (angular.element === window.jQuery);

  GestureHandler.prototype = {
    options: {},
    // jQuery listeners don't work with custom DOMEvents, so we have to dispatch events
    // differently when jQuery is loaded
    dispatchEvent: hasJQuery ?  jQueryDispatchEvent : nativeDispatchEvent,

    // These are overridden by the registered handler
    onSetup: angular.noop,
    onCleanup: angular.noop,
    onStart: angular.noop,
    onMove: angular.noop,
    onEnd: angular.noop,
    onCancel: angular.noop,

    // onStart sets up a new state for the handler, which includes options from the
    // nearest registered parent element of ev.target.
    start: function (ev, pointer) {
      if (this.state.isRunning) return;
      var parentTarget = this.getNearestParent(ev.target);
      // Get the options from the nearest registered parent
      var parentTargetOptions = parentTarget && parentTarget.$mdGesture[this.name] || {};

      this.state = {
        isRunning: true,
        // Override the default options with the nearest registered parent's options
        options: angular.extend({}, this.options, parentTargetOptions),
        // Pass in the registered parent node to the state so the onStart listener can use
        registeredParent: parentTarget
      };
      this.onStart(ev, pointer);
    },
    move: function (ev, pointer) {
      if (!this.state.isRunning) return;
      this.onMove(ev, pointer);
    },
    end: function (ev, pointer) {
      if (!this.state.isRunning) return;
      this.state.isRunning = false;
      this.onEnd(ev, pointer);
    },
    cancel: function (ev, pointer) {
      this.onCancel(ev, pointer);
      this.state = {};
    },

    // Find and return the nearest parent element that has been registered to
    // listen for this handler via $mdGesture.register(element, 'handlerName').
    getNearestParent: function (node) {
      var current = node;
      while (current) {
        if ((current.$mdGesture || {})[this.name]) {
          return current;
        }
        current = current.parentNode;
      }
      return null;
    },

    // Called from $mdGesture.register when an element registers itself with a handler.
    // Store the options the user gave on the DOMElement itself. These options will
    // be retrieved with getNearestParent when the handler starts.
    registerElement: function (element, options) {
      var self = this;
      element[0].$mdGesture = element[0].$mdGesture || {};
      element[0].$mdGesture[this.name] = options || {};
      element.on('$destroy', onDestroy);

      self.onSetup(element, options || {});

      return onDestroy;

      function onDestroy() {
        delete element[0].$mdGesture[self.name];
        element.off('$destroy', onDestroy);

        self.onCleanup(element, options || {});
      }
    }
  };

  return GestureHandler;

  /**
   * Dispatch an event with jQuery
   * TODO: Make sure this sends bubbling events
   *
   * @param srcEvent the original DOM touch event that started this.
   * @param eventType the name of the custom event to send (eg 'click' or '$md.drag')
   * @param eventPointer the pointer object that matches this event.
   */
  function jQueryDispatchEvent(srcEvent, eventType, eventPointer) {
    eventPointer = eventPointer || pointer;
    var eventObj = new angular.element.Event(eventType);

    eventObj.$material = true;
    eventObj.pointer = eventPointer;
    eventObj.srcEvent = srcEvent;

    angular.extend(eventObj, {
      clientX: eventPointer.x,
      clientY: eventPointer.y,
      screenX: eventPointer.x,
      screenY: eventPointer.y,
      pageX: eventPointer.x,
      pageY: eventPointer.y,
      ctrlKey: srcEvent.ctrlKey,
      altKey: srcEvent.altKey,
      shiftKey: srcEvent.shiftKey,
      metaKey: srcEvent.metaKey
    });
    angular.element(eventPointer.target).trigger(eventObj);
  }

  /**
   * NOTE: nativeDispatchEvent is very performance sensitive.
   * @param srcEvent the original DOM touch event that started this.
   * @param eventType the name of the custom event to send (eg 'click' or '$md.drag')
   * @param eventPointer the pointer object that matches this event.
   */
  function nativeDispatchEvent(srcEvent, eventType, eventPointer) {
    eventPointer = eventPointer || pointer;
    var eventObj;

    if (eventType === 'click' || eventType === 'mouseup' || eventType === 'mousedown') {
      if (typeof window.MouseEvent === "function") {
        eventObj = new MouseEvent(eventType, {
          bubbles: true,
          cancelable: true,
          screenX: Number(srcEvent.screenX),
          screenY: Number(srcEvent.screenY),
          clientX: Number(eventPointer.x),
          clientY: Number(eventPointer.y),
          ctrlKey: srcEvent.ctrlKey,
          altKey: srcEvent.altKey,
          shiftKey: srcEvent.shiftKey,
          metaKey: srcEvent.metaKey,
          button: srcEvent.button,
          buttons: srcEvent.buttons,
          relatedTarget: srcEvent.relatedTarget || null
        });
      } else {
        eventObj = document.createEvent('MouseEvents');
        // This has been deprecated
        // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/initMouseEvent
        eventObj.initMouseEvent(
          eventType, true, true, window, srcEvent.detail,
          eventPointer.x, eventPointer.y, eventPointer.x, eventPointer.y,
          srcEvent.ctrlKey, srcEvent.altKey, srcEvent.shiftKey, srcEvent.metaKey,
          srcEvent.button, srcEvent.relatedTarget || null
        );
      }
    } else {
      if (typeof window.CustomEvent === "function") {
        eventObj = new CustomEvent(eventType, {
          bubbles: true,
          cancelable: true,
          detail: {}
        });
      } else {
        // This has been deprecated
        // https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/initCustomEvent
        eventObj = document.createEvent('CustomEvent');
        eventObj.initCustomEvent(eventType, true, true, {});
      }
    }
    eventObj.$material = true;
    eventObj.pointer = eventPointer;
    eventObj.srcEvent = srcEvent;
    eventPointer.target.dispatchEvent(eventObj);
  }
}

/**
 * Attach Gestures: hook document and check shouldHijack clicks
 * @ngInject
 */
function attachToDocument($mdGesture, $$MdGestureHandler, $mdUtil) {
  if (disableAllGestures) {
    return;
  }

  if (!isInitialized && $mdGesture.isHijackingClicks) {
    /*
     * If hijack clicks is true, we preventDefault any click that wasn't
     * sent by AngularJS Material. This is because on older Android & iOS, a false, or 'ghost',
     * click event will be sent ~400ms after a touchend event happens.
     * The only way to know if this click is real is to prevent any normal
     * click events, and add a flag to events sent by material so we know not to prevent those.
     *
     * Two exceptions to click events that should be prevented are:
     *  - click events sent by the keyboard (eg form submit)
     *  - events that originate from an Ionic app
     */
    document.addEventListener('click'    , clickHijacker     , true);
    document.addEventListener('mouseup'  , mouseInputHijacker, true);
    document.addEventListener('mousedown', mouseInputHijacker, true);
    document.addEventListener('focus'    , mouseInputHijacker, true);

    isInitialized = true;
  }

  function mouseInputHijacker(ev) {
    var isKeyClick = !ev.clientX && !ev.clientY;

    if (
      !isKeyClick &&
      !ev.$material &&
      !ev.isIonicTap &&
      !isInputEventFromLabelClick(ev) &&
      (ev.type !== 'mousedown' || (!canFocus(ev.target) && !canFocus(document.activeElement)))
    ) {
      ev.preventDefault();
      ev.stopPropagation();
    }
  }

  /**
   * Ignore click events that don't come from AngularJS Material, Ionic, Input Label clicks,
   * or key presses that generate click events. This helps to ignore the ghost tap events on
   * older mobile browsers that get sent after a 300-400ms delay.
   * @param ev MouseEvent or modified MouseEvent with $material, pointer, and other fields
   */
  function clickHijacker(ev) {
    var isKeyClick;
    if ($mdUtil.isIos) {
      isKeyClick = angular.isDefined(ev.webkitForce) && ev.webkitForce === 0;
    } else {
      isKeyClick = ev.clientX === 0 && ev.clientY === 0;
    }
    if (!isKeyClick && !ev.$material && !ev.isIonicTap && !isInputEventFromLabelClick(ev)) {
      ev.preventDefault();
      ev.stopPropagation();
      lastLabelClickPos = null;
    } else {
      lastLabelClickPos = null;
      if (ev.target.tagName.toLowerCase() === 'label') {
        lastLabelClickPos = {x: ev.x, y: ev.y};
      }
    }
  }


  // Listen to all events to cover all platforms.
  var START_EVENTS = 'mousedown touchstart pointerdown';
  var MOVE_EVENTS = 'mousemove touchmove pointermove';
  var END_EVENTS = 'mouseup mouseleave touchend touchcancel pointerup pointercancel';

  angular.element(document)
    .on(START_EVENTS, gestureStart)
    .on(MOVE_EVENTS, gestureMove)
    .on(END_EVENTS, gestureEnd)
    // For testing
    .on('$$mdGestureReset', function gestureClearCache () {
      lastPointer = pointer = null;
    });

  /**
   * When a DOM event happens, run all registered gesture handlers' lifecycle
   * methods which match the DOM event.
   * Eg. when a 'touchstart' event happens, runHandlers('start') will call and
   * run `handler.cancel()` and `handler.start()` on all registered handlers.
   */
  function runHandlers(handlerEvent, event) {
    var handler;
    for (var name in HANDLERS) {
      handler = HANDLERS[name];
      if (handler instanceof $$MdGestureHandler) {

        if (handlerEvent === 'start') {
          // Run cancel to reset any handlers' state
          handler.cancel();
        }
        handler[handlerEvent](event, pointer);
      }
    }
  }

  /*
   * gestureStart vets if a start event is legitimate (and not part of a 'ghost click' from iOS/Android)
   * If it is legitimate, we initiate the pointer state and mark the current pointer's type
   * For example, for a touchstart event, mark the current pointer as a 'touch' pointer, so mouse events
   * won't effect it.
   */
  function gestureStart(ev) {
    // If we're already touched down, abort
    if (pointer) return;

    var now = +Date.now();

    // iOS & old android bug: after a touch event, a click event is sent 350 ms later.
    // If <400ms have passed, don't allow an event of a different type than the previous event
    if (lastPointer && !typesMatch(ev, lastPointer) && (now - lastPointer.endTime < 1500)) {
      return;
    }

    pointer = makeStartPointer(ev);

    runHandlers('start', ev);
  }

  /**
   * If a move event happens of the right type, update the pointer and run all the move handlers.
   * "of the right type": if a mousemove happens but our pointer started with a touch event, do
   * nothing.
   */
  function gestureMove(ev) {
    if (!pointer || !typesMatch(ev, pointer)) return;

    updatePointerState(ev, pointer);
    runHandlers('move', ev);
  }

  /**
   * If an end event happens of the right type, update the pointer, run endHandlers, and save the
   * pointer as 'lastPointer'.
   */
  function gestureEnd(ev) {
    if (!pointer || !typesMatch(ev, pointer)) return;

    updatePointerState(ev, pointer);
    pointer.endTime = +Date.now();

    if (ev.type !== 'pointercancel') {
      runHandlers('end', ev);
    }

    lastPointer = pointer;
    pointer = null;
  }

}

// ********************
// Module Functions
// ********************

/*
 * Initiate the pointer. x, y, and the pointer's type.
 */
function makeStartPointer(ev) {
  var point = getEventPoint(ev);
  var startPointer = {
    startTime: +Date.now(),
    target: ev.target,
    // 'p' for pointer events, 'm' for mouse, 't' for touch
    type: ev.type.charAt(0)
  };
  startPointer.startX = startPointer.x = point.pageX;
  startPointer.startY = startPointer.y = point.pageY;
  return startPointer;
}

/*
 * return whether the pointer's type matches the event's type.
 * Eg if a touch event happens but the pointer has a mouse type, return false.
 */
function typesMatch(ev, pointer) {
  return ev && pointer && ev.type.charAt(0) === pointer.type;
}

/**
 * Gets whether the given event is an input event that was caused by clicking on an
 * associated label element.
 *
 * This is necessary because the browser will, upon clicking on a label element, fire an
 * *extra* click event on its associated input (if any). mdGesture is able to flag the label
 * click as with `$material` correctly, but not the second input click.
 *
 * In order to determine whether an input event is from a label click, we compare the (x, y) for
 * the event to the (x, y) for the most recent label click (which is cleared whenever a non-label
 * click occurs). Unfortunately, there are no event properties that tie the input and the label
 * together (such as relatedTarget).
 *
 * @param {MouseEvent} event
 * @returns {boolean}
 */
function isInputEventFromLabelClick(event) {
  return lastLabelClickPos
      && lastLabelClickPos.x === event.x
      && lastLabelClickPos.y === event.y;
}

/*
 * Update the given pointer based upon the given DOMEvent.
 * Distance, velocity, direction, duration, etc
 */
function updatePointerState(ev, pointer) {
  var point = getEventPoint(ev);
  var x = pointer.x = point.pageX;
  var y = pointer.y = point.pageY;

  pointer.distanceX = x - pointer.startX;
  pointer.distanceY = y - pointer.startY;
  pointer.distance = Math.sqrt(
    pointer.distanceX * pointer.distanceX + pointer.distanceY * pointer.distanceY
  );

  pointer.directionX = pointer.distanceX > 0 ? 'right' : pointer.distanceX < 0 ? 'left' : '';
  pointer.directionY = pointer.distanceY > 0 ? 'down' : pointer.distanceY < 0 ? 'up' : '';

  pointer.duration = +Date.now() - pointer.startTime;
  pointer.velocityX = pointer.distanceX / pointer.duration;
  pointer.velocityY = pointer.distanceY / pointer.duration;
}

/**
 * Normalize the point where the DOM event happened whether it's touch or mouse.
 * @returns point event obj with pageX and pageY on it.
 */
function getEventPoint(ev) {
  ev = ev.originalEvent || ev; // support jQuery events
  return (ev.touches && ev.touches[0]) ||
    (ev.changedTouches && ev.changedTouches[0]) ||
    ev;
}

/** Checks whether an element can be focused. */
function canFocus(element) {
  return (
    !!element &&
    element.getAttribute('tabindex') !== '-1' &&
    !element.hasAttribute('disabled') &&
    (
      element.hasAttribute('tabindex') ||
      element.hasAttribute('href') ||
      element.isContentEditable ||
      ['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA', 'VIDEO', 'AUDIO'].indexOf(element.nodeName) !== -1
    )
  );
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.interaction
 * @description
 * User interaction detection to provide proper accessibility.
 */
MdInteractionService.$inject = ["$timeout", "$mdUtil", "$rootScope"];
angular
  .module('material.core.interaction', [])
  .service('$mdInteraction', MdInteractionService);


/**
 * @ngdoc service
 * @name $mdInteraction
 * @module material.core.interaction
 *
 * @description
 *
 * Service which keeps track of the last interaction type and validates them for several browsers.
 * The service hooks into the document's body and listens for touch, mouse and keyboard events.
 *
 * The most recent interaction type can be retrieved by calling the `getLastInteractionType` method.
 *
 * Here is an example markup for using the interaction service.
 *
 * <hljs lang="js">
 *   var lastType = $mdInteraction.getLastInteractionType();
 *
 *   if (lastType === 'keyboard') {
 *     // We only restore the focus for keyboard users.
 *     restoreFocus();
 *   }
 * </hljs>
 *
 */
function MdInteractionService($timeout, $mdUtil, $rootScope) {
  this.$timeout = $timeout;
  this.$mdUtil = $mdUtil;
  this.$rootScope = $rootScope;

  // IE browsers can also trigger pointer events, which also leads to an interaction.
  this.pointerEvent = 'MSPointerEvent' in window ? 'MSPointerDown' : 'PointerEvent' in window ? 'pointerdown' : null;
  this.bodyElement = angular.element(document.body);
  this.isBuffering = false;
  this.bufferTimeout = null;
  this.lastInteractionType = null;
  this.lastInteractionTime = null;
  this.inputHandler = this.onInputEvent.bind(this);
  this.bufferedInputHandler = this.onBufferInputEvent.bind(this);

  // Type Mappings for the different events
  // There will be three three interaction types
  // `keyboard`, `mouse` and `touch`
  // type `pointer` will be evaluated in `pointerMap` for IE Browser events
  this.inputEventMap = {
    'keydown': 'keyboard',
    'mousedown': 'mouse',
    'mouseenter': 'mouse',
    'touchstart': 'touch',
    'pointerdown': 'pointer',
    'MSPointerDown': 'pointer'
  };

  // IE PointerDown events will be validated in `touch` or `mouse`
  // Index numbers referenced here: https://msdn.microsoft.com/library/windows/apps/hh466130.aspx
  this.iePointerMap = {
    2: 'touch',
    3: 'touch',
    4: 'mouse'
  };

  this.initializeEvents();
  this.$rootScope.$on('$destroy', this.deregister.bind(this));
}

/**
 * Removes all event listeners created by $mdInteration on the
 * body element.
 */
MdInteractionService.prototype.deregister = function() {

    this.bodyElement.off('keydown mousedown', this.inputHandler);

    if ('ontouchstart' in document.documentElement) {
      this.bodyElement.off('touchstart', this.bufferedInputHandler);
    }

    if (this.pointerEvent) {
      this.bodyElement.off(this.pointerEvent, this.inputHandler);
    }

};

/**
 * Initializes the interaction service, by registering all interaction events to the
 * body element.
 */
MdInteractionService.prototype.initializeEvents = function() {

  this.bodyElement.on('keydown mousedown', this.inputHandler);

  if ('ontouchstart' in document.documentElement) {
    this.bodyElement.on('touchstart', this.bufferedInputHandler);
  }

  if (this.pointerEvent) {
    this.bodyElement.on(this.pointerEvent, this.inputHandler);
  }

};

/**
 * Event listener for normal interaction events, which should be tracked.
 * @param event {MouseEvent|KeyboardEvent|PointerEvent|TouchEvent}
 */
MdInteractionService.prototype.onInputEvent = function(event) {
  if (this.isBuffering) {
    return;
  }

  var type = this.inputEventMap[event.type];

  if (type === 'pointer') {
    type = this.iePointerMap[event.pointerType] || event.pointerType;
  }

  this.lastInteractionType = type;
  this.lastInteractionTime = this.$mdUtil.now();
};

/**
 * Event listener for interaction events which should be buffered (touch events).
 * @param event {TouchEvent}
 */
MdInteractionService.prototype.onBufferInputEvent = function(event) {
  this.$timeout.cancel(this.bufferTimeout);

  this.onInputEvent(event);
  this.isBuffering = true;

  // The timeout of 650ms is needed to delay the touchstart, because otherwise the touch will call
  // the `onInput` function multiple times.
  this.bufferTimeout = this.$timeout(function() {
    this.isBuffering = false;
  }.bind(this), 650, false);

};

/**
 * @ngdoc method
 * @name $mdInteraction#getLastInteractionType
 * @description Retrieves the last interaction type triggered in body.
 * @returns {string|null} Last interaction type.
 */
MdInteractionService.prototype.getLastInteractionType = function() {
  return this.lastInteractionType;
};

/**
 * @ngdoc method
 * @name $mdInteraction#isUserInvoked
 * @description Method to detect whether any interaction happened recently or not.
 * @param {number=} checkDelay Time to check for any interaction to have been triggered.
 * @returns {boolean} Whether there was any interaction or not.
 */
MdInteractionService.prototype.isUserInvoked = function(checkDelay) {
  var delay = angular.isNumber(checkDelay) ? checkDelay : 15;

  // Check for any interaction to be within the specified check time.
  return this.lastInteractionTime >= this.$mdUtil.now() - delay;
};

})();
(function(){
"use strict";

angular.module('material.core')
  .provider('$$interimElement', InterimElementProvider);

/**
 * @ngdoc service
 * @name $$interimElementProvider
 * @module material.core.interimElement
 *
 * @description
 *
 * Factory that constructs `$$interimElement.$service` services.
 * Used internally in material design for elements that appear on screen temporarily.
 * The service provides a promise-like API for interacting with the temporary
 * elements.
 *
 * <hljs lang="js">
 *   app.service('$mdToast', function($$interimElement) {
 *     var $mdToast = $$interimElement(toastDefaultOptions);
 *     return $mdToast;
 *   });
 * </hljs>
 *
 * @param {object=} defaultOptions Options used by default for the `show` method on the service.
 *
 * @returns {$$interimElement.$service}
 */

function InterimElementProvider() {
  InterimElementFactory.$inject = ["$document", "$q", "$rootScope", "$timeout", "$rootElement", "$animate", "$mdUtil", "$mdCompiler", "$mdTheming", "$injector", "$exceptionHandler"];
  createInterimElementProvider.$get = InterimElementFactory;
  return createInterimElementProvider;

  /**
   * Returns a new provider which allows configuration of a new interimElement
   * service. Allows configuration of default options & methods for options,
   * as well as configuration of 'preset' methods (eg dialog.basic(): basic is a preset method)
   */
  function createInterimElementProvider(interimFactoryName) {
    factory.$inject = ["$$interimElement", "$injector"];
    var EXPOSED_METHODS = ['onHide', 'onShow', 'onRemove'];

    var customMethods = {};
    var providerConfig = {
      presets: {}
    };

    var provider = {
      setDefaults: setDefaults,
      addPreset: addPreset,
      addMethod: addMethod,
      $get: factory
    };

    /**
     * all interim elements will come with the 'build' preset
     */
    provider.addPreset('build', {
      methods: ['controller', 'controllerAs', 'resolve', 'multiple',
        'template', 'templateUrl', 'themable', 'transformTemplate', 'parent', 'contentElement']
    });

    return provider;

    /**
     * Save the configured defaults to be used when the factory is instantiated
     */
    function setDefaults(definition) {
      providerConfig.optionsFactory = definition.options;
      providerConfig.methods = (definition.methods || []).concat(EXPOSED_METHODS);
      return provider;
    }

    /**
     * Add a method to the factory that isn't specific to any interim element operations
     */
    function addMethod(name, fn) {
      customMethods[name] = fn;
      return provider;
    }

    /**
     * Save the configured preset to be used when the factory is instantiated
     */
    function addPreset(name, definition) {
      definition = definition || {};
      definition.methods = definition.methods || [];
      definition.options = definition.options || function() { return {}; };

      if (/^cancel|hide|show$/.test(name)) {
        throw new Error("Preset '" + name + "' in " + interimFactoryName + " is reserved!");
      }
      if (definition.methods.indexOf('_options') > -1) {
        throw new Error("Method '_options' in " + interimFactoryName + " is reserved!");
      }
      providerConfig.presets[name] = {
        methods: definition.methods.concat(EXPOSED_METHODS),
        optionsFactory: definition.options,
        argOption: definition.argOption
      };
      return provider;
    }

    function addPresetMethod(presetName, methodName, method) {
      providerConfig.presets[presetName][methodName] = method;
    }

    /**
     * Create a factory that has the given methods & defaults implementing interimElement
     */
    /* @ngInject */
    function factory($$interimElement, $injector) {
      var defaultMethods;
      var defaultOptions;
      var interimElementService = $$interimElement();

      /*
       * publicService is what the developer will be using.
       * It has methods hide(), cancel(), show(), build(), and any other
       * presets which were set during the config phase.
       */
      var publicService = {
        hide: interimElementService.hide,
        cancel: interimElementService.cancel,
        show: showInterimElement,

        // Special internal method to destroy an interim element without animations
        // used when navigation changes causes a $scope.$destroy() action
        destroy : destroyInterimElement
      };


      defaultMethods = providerConfig.methods || [];
      // This must be invoked after the publicService is initialized
      defaultOptions = invokeFactory(providerConfig.optionsFactory, {});

      // Copy over the simple custom methods
      angular.forEach(customMethods, function(fn, name) {
        publicService[name] = fn;
      });

      angular.forEach(providerConfig.presets, function(definition, name) {
        var presetDefaults = invokeFactory(definition.optionsFactory, {});
        var presetMethods = (definition.methods || []).concat(defaultMethods);

        // Every interimElement built with a preset has a field called `$type`,
        // which matches the name of the preset.
        // Eg in preset 'confirm', options.$type === 'confirm'
        angular.extend(presetDefaults, { $type: name });

        // This creates a preset class which has setter methods for every
        // method given in the `.addPreset()` function, as well as every
        // method given in the `.setDefaults()` function.
        //
        // @example
        // .setDefaults({
        //   methods: ['hasBackdrop', 'clickOutsideToClose', 'escapeToClose', 'targetEvent'],
        //   options: dialogDefaultOptions
        // })
        // .addPreset('alert', {
        //   methods: ['title', 'ok'],
        //   options: alertDialogOptions
        // })
        //
        // Set values will be passed to the options when interimElement.show() is called.
        function Preset(opts) {
          this._options = angular.extend({}, presetDefaults, opts);
        }
        angular.forEach(presetMethods, function(name) {
          Preset.prototype[name] = function(value) {
            this._options[name] = value;
            return this;
          };
        });

        // Create shortcut method for one-linear methods
        if (definition.argOption) {
          var methodName = 'show' + name.charAt(0).toUpperCase() + name.slice(1);
          publicService[methodName] = function(arg) {
            var config = publicService[name](arg);
            return publicService.show(config);
          };
        }

        // eg $mdDialog.alert() will return a new alert preset
        publicService[name] = function(arg) {
          // If argOption is supplied, eg `argOption: 'content'`, then we assume
          // if the argument is not an options object then it is the `argOption` option.
          //
          // @example `$mdToast.simple('hello')` // sets options.content to hello
          //                                     // because argOption === 'content'
          if (arguments.length && definition.argOption &&
              !angular.isObject(arg) && !angular.isArray(arg))  {

            return (new Preset())[definition.argOption](arg);

          } else {
            return new Preset(arg);
          }

        };
      });

      return publicService;

      /**
       *
       */
      function showInterimElement(opts) {
        // opts is either a preset which stores its options on an _options field,
        // or just an object made up of options
        opts = opts || { };
        if (opts._options) opts = opts._options;

        return interimElementService.show(
          angular.extend({}, defaultOptions, opts)
        );
      }

      /**
       *  Special method to hide and destroy an interimElement WITHOUT
       *  any 'leave` or hide animations ( an immediate force hide/remove )
       *
       *  NOTE: This calls the onRemove() subclass method for each component...
       *  which must have code to respond to `options.$destroy == true`
       */
      function destroyInterimElement(opts) {
          return interimElementService.destroy(opts);
      }

      /**
       * Helper to call $injector.invoke with a local of the factory name for
       * this provider.
       * If an $mdDialog is providing options for a dialog and tries to inject
       * $mdDialog, a circular dependency error will happen.
       * We get around that by manually injecting $mdDialog as a local.
       */
      function invokeFactory(factory, defaultVal) {
        var locals = {};
        locals[interimFactoryName] = publicService;
        return $injector.invoke(factory || function() { return defaultVal; }, {}, locals);
      }
    }
  }

  /* @ngInject */
  function InterimElementFactory($document, $q, $rootScope, $timeout, $rootElement, $animate,
                                 $mdUtil, $mdCompiler, $mdTheming, $injector, $exceptionHandler) {
    return function createInterimElementService() {
      var SHOW_CANCELLED = false;

      /**
       * @ngdoc service
       * @name $$interimElementProvider.$service
       *
       * @description
       * A service used to control inserting and removing of an element from the DOM.
       * It is used by $mdBottomSheet, $mdDialog, $mdToast, $mdMenu, $mdPanel, and $mdSelect.
       */
      var service;

      var showPromises = []; // Promises for the interim's which are currently opening.
      var hidePromises = []; // Promises for the interim's which are currently hiding.
      var showingInterims = []; // Interim elements which are currently showing up.

      // Publish instance $$interimElement service;
      return service = {
        show: show,
        hide: waitForInterim(hide),
        cancel: waitForInterim(cancel),
        destroy : destroy,
        $injector_: $injector
      };

      /**
       * @ngdoc method
       * @name $$interimElementProvider.$service#show
       * @kind function
       *
       * @description
       * Adds the `$interimElement` to the DOM and returns a special promise that will be resolved
       * or rejected with hide or cancel, respectively.
       *
       * @param {object} options map of options and values
       * @returns {Promise} a Promise that will be resolved when hide() is called or rejected when
       *  cancel() is called.
       */
      function show(options) {
        options = options || {};
        var interimElement = new InterimElement(options || {});

        // When an interim element is currently showing, we have to cancel it.
        // Just hiding it, will resolve the InterimElement's promise, the promise should be
        // rejected instead.
        var hideAction = options.multiple ? $q.resolve() : $q.all(showPromises);

        if (!options.multiple) {
          // Wait for all opening interim's to finish their transition.
          hideAction = hideAction.then(function() {
            // Wait for all closing and showing interim's to be completely closed.
            var promiseArray = hidePromises.concat(showingInterims.map(service.cancel));
            return $q.all(promiseArray);
          });
        }

        var showAction = hideAction.then(function() {

          return interimElement
            .show()
            .then(function () {
              showingInterims.push(interimElement);
            })
            .catch(function (reason) {
              return reason;
            })
            .finally(function() {
              showPromises.splice(showPromises.indexOf(showAction), 1);
            });

        });

        showPromises.push(showAction);

        // In AngularJS 1.6+, exceptions inside promises will cause a rejection. We need to handle
        // the rejection and only log it if it's an error.
        interimElement.deferred.promise.catch(function(fault) {
          if (fault instanceof Error) {
            $exceptionHandler(fault);
          }

          return fault;
        });

        // Return a promise that will be resolved when the interim
        // element is hidden or cancelled...
        return interimElement.deferred.promise;
      }

      /**
       * @ngdoc method
       * @name $$interimElementProvider.$service#hide
       * @kind function
       *
       * @description
       * Removes the `$interimElement` from the DOM and resolves the Promise returned from `show()`.
       *
       * @param {*} reason Data used to resolve the Promise
       * @param {object} options map of options and values
       * @returns {Promise} a Promise that will be resolved after the element has been removed
       *  from the DOM.
       */
      function hide(reason, options) {
        options = options || {};

        if (options.closeAll) {
          // We have to make a shallow copy of the array, because otherwise the map will break.
          return $q.all(showingInterims.slice().reverse().map(closeElement));
        } else if (options.closeTo !== undefined) {
          return $q.all(showingInterims.slice(options.closeTo).map(closeElement));
        }

        // Hide the latest showing interim element.
        return closeElement(showingInterims[showingInterims.length - 1]);

        /**
         * @param {InterimElement} interim element to close
         * @returns {Promise<InterimElement>}
         */
        function closeElement(interim) {
          if (!interim) {
            return $q.when(reason);
          }

          var hideAction = interim
            .remove(reason, false, options || { })
            .catch(function(reason) { return reason; })
            .finally(function() {
              hidePromises.splice(hidePromises.indexOf(hideAction), 1);
            });

          showingInterims.splice(showingInterims.indexOf(interim), 1);
          hidePromises.push(hideAction);

          return interim.deferred.promise;
        }
      }

      /**
       * @ngdoc method
       * @name $$interimElementProvider.$service#cancel
       * @kind function
       *
       * @description
       * Removes the `$interimElement` from the DOM and rejects the Promise returned from `show()`.
       *
       * @param {*} reason Data used to resolve the Promise
       * @param {object} options map of options and values
       * @returns {Promise} Promise that will be resolved after the element has been removed
       *  from the DOM.
       */
      function cancel(reason, options) {
        var interim = showingInterims.pop();
        if (!interim) {
          return $q.when(reason);
        }

        var cancelAction = interim
          .remove(reason, true, options || {})
          .catch(function(reason) { return reason; })
          .finally(function() {
            hidePromises.splice(hidePromises.indexOf(cancelAction), 1);
          });

        hidePromises.push(cancelAction);

        // Since AngularJS 1.6.7, promises will be logged to $exceptionHandler when the promise
        // is not handling the rejection. We create a pseudo catch handler, which will prevent the
        // promise from being logged to the $exceptionHandler.
        return interim.deferred.promise.catch(angular.noop);
      }

      /**
       * Creates a function to wait for at least one interim element to be available.
       * @param callbackFn Function to be used as callback
       * @returns {Function}
       */
      function waitForInterim(callbackFn) {
        return function() {
          var fnArguments = arguments;

          if (!showingInterims.length) {
            // When there are still interim's opening, then wait for the first interim element to
            // finish its open animation.
            if (showPromises.length) {
              return showPromises[0].finally(function () {
                return callbackFn.apply(service, fnArguments);
              });
            }

            return $q.when("No interim elements currently showing up.");
          }

          return callbackFn.apply(service, fnArguments);
        };
      }

      /**
       * @ngdoc method
       * @name $$interimElementProvider.$service#destroy
       * @kind function
       *
       * Special method to quick-remove the interim element without running animations. This is
       * useful when the parent component has been or is being destroyed.
       *
       * Note: interim elements are in "interim containers".
       */
      function destroy(targetEl) {
        var interim = !targetEl ? showingInterims.shift() : null;

        var parentEl = angular.element(targetEl).length && angular.element(targetEl)[0].parentNode;

        if (parentEl) {
          // Try to find the interim in the stack which corresponds to the supplied DOM element.
          var filtered = showingInterims.filter(function(entry) {
            return entry.options.element[0] === parentEl;
          });

          // Note: This function might be called when the element already has been removed,
          // in which case we won't find any matches.
          if (filtered.length) {
            interim = filtered[0];
            showingInterims.splice(showingInterims.indexOf(interim), 1);
          }
        }

        return interim ? interim.remove(SHOW_CANCELLED, false, { '$destroy': true }) :
                         $q.when(SHOW_CANCELLED);
      }

      /*
       * Internal Interim Element Object
       * Used internally to manage the DOM element and related data
       */
      function InterimElement(options) {
        var self, element, showAction = $q.when(true);

        options = configureScopeAndTransitions(options);

        return self = {
          options : options,
          deferred: $q.defer(),
          show    : createAndTransitionIn,
          remove  : transitionOutAndRemove
        };

        /**
         * Compile, link, and show this interim element
         * Use optional autoHided and transition-in effects
         */
        function createAndTransitionIn() {
          return $q(function(resolve, reject) {

            // Trigger onCompiling callback before the compilation starts.
            // This is useful, when modifying options, which can be influenced by developers.
            options.onCompiling && options.onCompiling(options);

            compileElement(options)
              .then(function(compiledData) {
                element = linkElement(compiledData, options);

                // Expose the cleanup function from the compiler.
                options.cleanupElement = compiledData.cleanup;

                showAction = showElement(element, options, compiledData.controller)
                  .then(resolve, rejectAll);
              }).catch(rejectAll);

            function rejectAll(fault) {
              // Force the '$md<xxx>.show()' promise to reject
              self.deferred.reject(fault);

              // Continue rejection propagation
              reject(fault);
            }
          });
        }

        /**
         * After the show process has finished/rejected:
         * - announce 'removing',
         * - perform the transition-out, and
         * - perform optional clean up scope.
         */
        function transitionOutAndRemove(response, isCancelled, opts) {

          // abort if the show() and compile failed
          if (!element) return $q.when(false);

          options = angular.extend(options || {}, opts || {});
          options.cancelAutoHide && options.cancelAutoHide();
          options.element.triggerHandler('$mdInterimElementRemove');

          if (options.$destroy === true) {

            return hideElement(options.element, options).then(function(){
              (isCancelled && rejectAll(response)) || resolveAll(response);
            });

          } else {
            $q.when(showAction).finally(function() {
              hideElement(options.element, options).then(function() {
                isCancelled ? rejectAll(response) : resolveAll(response);
              }, rejectAll);
            });

            return self.deferred.promise;
          }


          /**
           * The `show()` returns a promise that will be resolved when the interim
           * element is hidden or cancelled...
           */
          function resolveAll(response) {
            self.deferred.resolve(response);
          }

          /**
           * Force the '$md<xxx>.show()' promise to reject
           */
          function rejectAll(fault) {
            self.deferred.reject(fault);
          }
        }

        /**
         * Prepare optional isolated scope and prepare $animate with default enter and leave
         * transitions for the new element instance.
         */
        function configureScopeAndTransitions(options) {
          options = options || { };
          if (options.template) {
            options.template = $mdUtil.processTemplate(options.template);
          }

          return angular.extend({
            preserveScope: false,
            cancelAutoHide : angular.noop,
            scope: options.scope || $rootScope.$new(options.isolateScope),

            /**
             * Default usage to enable $animate to transition-in; can be easily overridden via 'options'
             */
            onShow: function transitionIn(scope, element, options) {
              return $animate.enter(element, options.parent);
            },

            /**
             * Default usage to enable $animate to transition-out; can be easily overridden via 'options'
             */
            onRemove: function transitionOut(scope, element) {
              // Element could be undefined if a new element is shown before
              // the old one finishes compiling.
              return element && $animate.leave(element) || $q.when();
            }
          }, options);

        }

        /**
         * Compile an element with a templateUrl, controller, and locals
         */
        function compileElement(options) {

          var compiled = !options.skipCompile ? $mdCompiler.compile(options) : null;

          return compiled || $q(function (resolve) {
              resolve({
                locals: {},
                link: function () {
                  return options.element;
                }
              });
            });
        }

        /**
         *  Link an element with compiled configuration
         */
        function linkElement(compileData, options){
          angular.extend(compileData.locals, options);

          var element = compileData.link(options.scope);

          // Search for parent at insertion time, if not specified
          options.element = element;
          options.parent = findParent(element, options);
          if (options.themable) $mdTheming(element);

          return element;
        }

        /**
         * Search for parent at insertion time, if not specified
         */
        function findParent(element, options) {
          var parent = options.parent;

          // Search for parent at insertion time, if not specified
          if (angular.isFunction(parent)) {
            parent = parent(options.scope, element, options);
          } else if (angular.isString(parent)) {
            parent = angular.element($document[0].querySelector(parent));
          } else {
            parent = angular.element(parent);
          }

          // If parent querySelector/getter function fails, or it's just null,
          // find a default.
          if (!(parent || {}).length) {
            var el;
            if ($rootElement[0] && $rootElement[0].querySelector) {
              el = $rootElement[0].querySelector(':not(svg) > body');
            }
            if (!el) el = $rootElement[0];
            if (el.nodeName == '#comment') {
              el = $document[0].body;
            }
            return angular.element(el);
          }

          return parent;
        }

        /**
         * If auto-hide is enabled, start timer and prepare cancel function
         */
        function startAutoHide() {
          var autoHideTimer, cancelAutoHide = angular.noop;

          if (options.hideDelay) {
            autoHideTimer = $timeout(service.hide, options.hideDelay) ;
            cancelAutoHide = function() {
              $timeout.cancel(autoHideTimer);
            };
          }

          // Cache for subsequent use
          options.cancelAutoHide = function() {
            cancelAutoHide();
            options.cancelAutoHide = undefined;
          };
        }

        /**
         * Show the element ( with transitions), notify complete and start
         * optional auto-Hide
         */
        function showElement(element, options, controller) {
          // Trigger onShowing callback before the `show()` starts
          var notifyShowing = options.onShowing || angular.noop;
          // Trigger onComplete callback when the `show()` finishes
          var notifyComplete = options.onComplete || angular.noop;

          // Necessary for consistency between AngularJS 1.5 and 1.6.
          try {
            notifyShowing(options.scope, element, options, controller);
          } catch (e) {
            return $q.reject(e);
          }

          return $q(function (resolve, reject) {
            try {
              // Start transitionIn
              $q.when(options.onShow(options.scope, element, options, controller))
                .then(function () {
                  notifyComplete(options.scope, element, options);
                  startAutoHide();

                  resolve(element);
                }, reject);

            } catch (e) {
              reject(e.message);
            }
          });
        }

        function hideElement(element, options) {
          var announceRemoving = options.onRemoving || angular.noop;

          return $q(function (resolve, reject) {
            try {
              // Start transitionIn
              var action = $q.when(options.onRemove(options.scope, element, options) || true);

              // Trigger callback *before* the remove operation starts
              announceRemoving(element, action);

              if (options.$destroy) {
                // For $destroy, onRemove should be synchronous
                resolve(element);

                if (!options.preserveScope && options.scope) {
                  // scope destroy should still be be done after the current digest is done
                  action.then(function() { options.scope.$destroy(); });
                }
              } else {
                // Wait until transition-out is done
                action.then(function () {
                  if (!options.preserveScope && options.scope) {
                    options.scope.$destroy();
                  }

                  resolve(element);
                }, reject);
              }
            } catch (e) {
              reject(e.message);
            }
          });
        }

      }
    };
  }
}

})();
(function(){
"use strict";

(function() {
  'use strict';

  var $mdUtil, $interpolate, $log;

  var SUFFIXES = /(-gt)?-(sm|md|lg|print)/g;
  var WHITESPACE = /\s+/g;

  var FLEX_OPTIONS = ['grow', 'initial', 'auto', 'none', 'noshrink', 'nogrow'];
  var LAYOUT_OPTIONS = ['row', 'column'];
  var ALIGNMENT_MAIN_AXIS= ["", "start", "center", "end", "stretch", "space-around", "space-between"];
  var ALIGNMENT_CROSS_AXIS= ["", "start", "center", "end", "stretch"];

  var config = {
    /**
     * Enable directive attribute-to-class conversions
     * Developers can use `<body md-layout-css />` to quickly
     * disable the Layout directives and prohibit the injection of Layout classNames
     */
    enabled: true,

    /**
     * List of mediaQuery breakpoints and associated suffixes
     *
     *   [
     *    { suffix: "sm", mediaQuery: "screen and (max-width: 599px)" },
     *    { suffix: "md", mediaQuery: "screen and (min-width: 600px) and (max-width: 959px)" }
     *   ]
     */
    breakpoints: []
  };

  registerLayoutAPI(angular.module('material.core.layout', ['ng']));

  /**
   *   registerLayoutAPI()
   *
   *   The original AngularJS Material Layout solution used attribute selectors and CSS.
   *
   *  ```html
   *  <div layout="column"> My Content </div>
   *  ```
   *
   *  ```css
   *  [layout] {
   *    box-sizing: border-box;
   *    display:flex;
   *  }
   *  [layout=column] {
   *    flex-direction : column
   *  }
   *  ```
   *
   *  Use of attribute selectors creates significant performance impacts in some
   *  browsers... mainly IE.
   *
   *  This module registers directives that allow the same layout attributes to be
   *  interpreted and converted to class selectors. The directive will add equivalent classes to each element that
   *  contains a Layout directive.
   *
   * ```html
   *   <div layout="column" class="layout layout-column"> My Content </div>
   *```
   *
   *  ```css
   *  .layout {
   *    box-sizing: border-box;
   *    display:flex;
   *  }
   *  .layout-column {
   *    flex-direction : column
   *  }
   *  ```
   */
  function registerLayoutAPI(module){
    var PREFIX_REGEXP = /^((?:x|data)[:\-_])/i;
    var SPECIAL_CHARS_REGEXP = /([:\-_]+(.))/g;

    // NOTE: these are also defined in constants::MEDIA_PRIORITY and constants::MEDIA
    var BREAKPOINTS     = ["", "xs", "gt-xs", "sm", "gt-sm", "md", "gt-md", "lg", "gt-lg", "xl", "print"];
    var API_WITH_VALUES = ["layout", "flex", "flex-order", "flex-offset", "layout-align"];
    var API_NO_VALUES   = ["show", "hide", "layout-padding", "layout-margin"];


    // Build directive registration functions for the standard Layout API... for all breakpoints.
    angular.forEach(BREAKPOINTS, function(mqb) {

      // Attribute directives with expected, observable value(s)
      angular.forEach(API_WITH_VALUES, function(name){
        var fullName = mqb ? name + "-" + mqb : name;
        module.directive(directiveNormalize(fullName), attributeWithObserve(fullName));
      });

      // Attribute directives with no expected value(s)
      angular.forEach(API_NO_VALUES, function(name){
        var fullName = mqb ? name + "-" + mqb : name;
        module.directive(directiveNormalize(fullName), attributeWithoutValue(fullName));
      });

    });

    // Register other, special directive functions for the Layout features:
    module

      .provider('$$mdLayout'     , function() {
        // Publish internal service for Layouts
        return {
          $get : angular.noop,
          validateAttributeValue : validateAttributeValue,
          validateAttributeUsage : validateAttributeUsage,
          /**
           * Easy way to disable/enable the Layout API.
           * When disabled, this stops all attribute-to-classname generations
           */
          disableLayouts  : function(isDisabled) {
            config.enabled =  (isDisabled !== true);
          }
        };
      })

      .directive('mdLayoutCss'        , disableLayoutDirective)
      .directive('ngCloak'            , buildCloakInterceptor('ng-cloak'))

      .directive('layoutWrap'   , attributeWithoutValue('layout-wrap'))
      .directive('layoutNowrap' , attributeWithoutValue('layout-nowrap'))
      .directive('layoutNoWrap' , attributeWithoutValue('layout-no-wrap'))
      .directive('layoutFill'   , attributeWithoutValue('layout-fill'))

      // !! Deprecated attributes: use the `-lt` (aka less-than) notations

      .directive('layoutLtMd'     , warnAttrNotSupported('layout-lt-md', true))
      .directive('layoutLtLg'     , warnAttrNotSupported('layout-lt-lg', true))
      .directive('flexLtMd'       , warnAttrNotSupported('flex-lt-md', true))
      .directive('flexLtLg'       , warnAttrNotSupported('flex-lt-lg', true))

      .directive('layoutAlignLtMd', warnAttrNotSupported('layout-align-lt-md'))
      .directive('layoutAlignLtLg', warnAttrNotSupported('layout-align-lt-lg'))
      .directive('flexOrderLtMd'  , warnAttrNotSupported('flex-order-lt-md'))
      .directive('flexOrderLtLg'  , warnAttrNotSupported('flex-order-lt-lg'))
      .directive('offsetLtMd'     , warnAttrNotSupported('flex-offset-lt-md'))
      .directive('offsetLtLg'     , warnAttrNotSupported('flex-offset-lt-lg'))

      .directive('hideLtMd'       , warnAttrNotSupported('hide-lt-md'))
      .directive('hideLtLg'       , warnAttrNotSupported('hide-lt-lg'))
      .directive('showLtMd'       , warnAttrNotSupported('show-lt-md'))
      .directive('showLtLg'       , warnAttrNotSupported('show-lt-lg'))

      // Determine if
      .config(detectDisabledLayouts);

    /**
     * Converts snake_case to camelCase.
     * Also there is special case for Moz prefix starting with upper case letter.
     * @param name Name to normalize
     */
    function directiveNormalize(name) {
      return name
        .replace(PREFIX_REGEXP, '')
        .replace(SPECIAL_CHARS_REGEXP, function(_, separator, letter, offset) {
          return offset ? letter.toUpperCase() : letter;
        });
    }

  }


  /**
    * Detect if any of the HTML tags has a [md-layouts-disabled] attribute;
    * If yes, then immediately disable all layout API features
    *
    * Note: this attribute should be specified on either the HTML or BODY tags
    */
   /**
    * @ngInject
    */
   function detectDisabledLayouts() {
     var isDisabled = !!document.querySelector('[md-layouts-disabled]');
     config.enabled = !isDisabled;
   }

  /**
   * Special directive that will disable ALL Layout conversions of layout
   * attribute(s) to classname(s).
   *
   * <link rel="stylesheet" href="angular-material.min.css">
   * <link rel="stylesheet" href="angular-material.layout.css">
   *
   * <body md-layout-css>
   *  ...
   * </body>
   *
   * Note: Using md-layout-css directive requires the developer to load the Material
   * Layout Attribute stylesheet (which only uses attribute selectors):
   *
   *       `angular-material.layout.css`
   *
   * Another option is to use the LayoutProvider to configure and disable the attribute
   * conversions; this would obviate the use of the `md-layout-css` directive
   *
   */
  function disableLayoutDirective() {
    // Return a 1x-only, first-match attribute directive
    config.enabled = false;

    return {
      restrict : 'A',
      priority : '900'
    };
  }

  /**
   * Tail-hook ngCloak to delay the uncloaking while Layout transformers
   * finish processing. Eliminates flicker with Material.Layouts
   */
  function buildCloakInterceptor(className) {
    return ['$timeout', function($timeout){
      return {
        restrict : 'A',
        priority : -10,   // run after normal ng-cloak
        compile  : function(element) {
          if (!config.enabled) return angular.noop;

          // Re-add the cloak
          element.addClass(className);

          return function(scope, element) {
            // Wait while layout injectors configure, then uncloak
            // NOTE: $rAF does not delay enough... and this is a 1x-only event,
            //       $timeout is acceptable.
            $timeout(function(){
              element.removeClass(className);
            }, 10, false);
          };
        }
      };
    }];
  }


  // *********************************************************************************
  //
  // These functions create registration functions for AngularJS Material Layout attribute directives
  // This provides easy translation to switch AngularJS Material attribute selectors to
  // CLASS selectors and directives; which has huge performance implications
  // for IE Browsers
  //
  // *********************************************************************************

  /**
   * Creates a directive registration function where a possible dynamic attribute
   * value will be observed/watched.
   * @param {string} className attribute name; eg `layout-gt-md` with value ="row"
   */
  function attributeWithObserve(className) {

    return ['$mdUtil', '$interpolate', "$log", function(_$mdUtil_, _$interpolate_, _$log_) {
      $mdUtil = _$mdUtil_;
      $interpolate = _$interpolate_;
      $log = _$log_;

      return {
        restrict: 'A',
        compile: function(element, attr) {
          var linkFn;
          if (config.enabled) {
            // immediately replace static (non-interpolated) invalid values...

            validateAttributeUsage(className, attr, element, $log);

            validateAttributeValue(className,
              getNormalizedAttrValue(className, attr, ""),
              buildUpdateFn(element, className, attr)
            );

            linkFn = translateWithValueToCssClass;
          }

          // Use for postLink to account for transforms after ng-transclude.
          return linkFn || angular.noop;
        }
      };
    }];

    /**
     * Add as transformed class selector(s), then
     * remove the deprecated attribute selector
     */
    function translateWithValueToCssClass(scope, element, attrs) {
      var updateFn = updateClassWithValue(element, className, attrs);
      var unwatch = attrs.$observe(attrs.$normalize(className), updateFn);

      updateFn(getNormalizedAttrValue(className, attrs, ""));
      scope.$on("$destroy", function() { unwatch(); });
    }
  }

  /**
   * Creates a registration function for AngularJS Material Layout attribute directive.
   * This is a `simple` transpose of attribute usage to class usage; where we ignore
   * any attribute value
   */
  function attributeWithoutValue(className) {
    return ['$mdUtil', '$interpolate', "$log", function(_$mdUtil_, _$interpolate_, _$log_) {
      $mdUtil = _$mdUtil_;
      $interpolate = _$interpolate_;
      $log = _$log_;

      return {
        restrict: 'A',
        compile: function(element, attr) {
          var linkFn;
          if (config.enabled) {
            // immediately replace static (non-interpolated) invalid values...

            validateAttributeValue(className,
              getNormalizedAttrValue(className, attr, ""),
              buildUpdateFn(element, className, attr)
            );

            translateToCssClass(null, element);

            // Use for postLink to account for transforms after ng-transclude.
            linkFn = translateToCssClass;
          }

          return linkFn || angular.noop;
        }
      };
    }];

    /**
     * Add as transformed class selector, then
     * remove the deprecated attribute selector
     */
    function translateToCssClass(scope, element) {
      element.addClass(className);
    }
  }



  /**
   * After link-phase, do NOT remove deprecated layout attribute selector.
   * Instead watch the attribute so interpolated data-bindings to layout
   * selectors will continue to be supported.
   *
   * $observe() the className and update with new class (after removing the last one)
   *
   * e.g. `layout="{{layoutDemo.direction}}"` will update...
   *
   * NOTE: The value must match one of the specified styles in the CSS.
   * For example `flex-gt-md="{{size}}`  where `scope.size == 47` will NOT work since
   * only breakpoints for 0, 5, 10, 15... 100, 33, 34, 66, 67 are defined.
   *
   */
  function updateClassWithValue(element, className) {
    var lastClass;

    return function updateClassFn(newValue) {
      var value = validateAttributeValue(className, newValue || "");
      if (angular.isDefined(value)) {
        if (lastClass) element.removeClass(lastClass);
        lastClass = !value ? className : className + "-" + value.trim().replace(WHITESPACE, "-");
        element.addClass(lastClass);
      }
    };
  }

  /**
   * Provide console warning that this layout attribute has been deprecated
   *
   */
  function warnAttrNotSupported(className) {
    var parts = className.split("-");
    return ["$log", function($log) {
      $log.warn(className + "has been deprecated. Please use a `" + parts[0] + "-gt-<xxx>` variant.");
      return angular.noop;
    }];
  }

  /**
   * Centralize warnings for known flexbox issues (especially IE-related issues)
   */
  function validateAttributeUsage(className, attr, element, $log){
    var message, usage, url;
    var nodeName = element[0].nodeName.toLowerCase();

    switch (className.replace(SUFFIXES,"")) {
      case "flex":
        if ((nodeName == "md-button") || (nodeName == "fieldset")){
          // @see https://github.com/philipwalton/flexbugs#9-some-html-elements-cant-be-flex-containers
          // Use <div flex> wrapper inside (preferred) or outside

          usage = "<" + nodeName + " " + className + "></" + nodeName + ">";
          url = "https://github.com/philipwalton/flexbugs#9-some-html-elements-cant-be-flex-containers";
          message = "Markup '{0}' may not work as expected in IE Browsers. Consult '{1}' for details.";

          $log.warn($mdUtil.supplant(message, [usage, url]));
        }
    }

  }


  /**
   * For the Layout attribute value, validate or replace with default
   * fallback value
   */
  function validateAttributeValue(className, value, updateFn) {
    var origValue = value;

    if (!needsInterpolation(value)) {
      switch (className.replace(SUFFIXES,"")) {
        case 'layout'        :
          if (!findIn(value, LAYOUT_OPTIONS)) {
            value = LAYOUT_OPTIONS[0];    // 'row';
          }
          break;

        case 'flex'          :
          if (!findIn(value, FLEX_OPTIONS)) {
            if (isNaN(value)) {
              value = '';
            }
          }
          break;

        case 'flex-offset' :
        case 'flex-order'    :
          if (!value || isNaN(+value)) {
            value = '0';
          }
          break;

        case 'layout-align'  :
          var axis = extractAlignAxis(value);
          value = $mdUtil.supplant("{main}-{cross}",axis);
          break;

        case 'layout-padding' :
        case 'layout-margin'  :
        case 'layout-fill'    :
        case 'layout-wrap'    :
        case 'layout-nowrap' :
          value = '';
          break;
      }

      if (value != origValue) {
        (updateFn || angular.noop)(value);
      }
    }

    return value ? value.trim() : "";
  }

  /**
   * Replace current attribute value with fallback value
   */
  function buildUpdateFn(element, className, attrs) {
    return function updateAttrValue(fallback) {
      if (!needsInterpolation(fallback)) {
        // Do not modify the element's attribute value; so
        // uses '<ui-layout layout="/api/sidebar.html" />' will not
        // be affected. Just update the attrs value.
        attrs[attrs.$normalize(className)] = fallback;
      }
    };
  }

  /**
   * See if the original value has interpolation symbols:
   * e.g.  flex-gt-md="{{triggerPoint}}"
   */
  function needsInterpolation(value) {
    return (value || "").indexOf($interpolate.startSymbol()) > -1;
  }

  function getNormalizedAttrValue(className, attrs, defaultVal) {
    var normalizedAttr = attrs.$normalize(className);
    return attrs[normalizedAttr] ? attrs[normalizedAttr].trim().replace(WHITESPACE, "-") : defaultVal || null;
  }

  function findIn(item, list, replaceWith) {
    item = replaceWith && item ? item.replace(WHITESPACE, replaceWith) : item;

    var found = false;
    if (item) {
      list.forEach(function(it) {
        it = replaceWith ? it.replace(WHITESPACE, replaceWith) : it;
        found = found || (it === item);
      });
    }
    return found;
  }

  function extractAlignAxis(attrValue) {
    var axis = {
      main : "start",
      cross: "stretch"
    }, values;

    attrValue = (attrValue || "");

    if (attrValue.indexOf("-") === 0 || attrValue.indexOf(" ") === 0) {
      // For missing main-axis values
      attrValue = "none" + attrValue;
    }

    values = attrValue.toLowerCase().trim().replace(WHITESPACE, "-").split("-");
    if (values.length && (values[0] === "space")) {
      // for main-axis values of "space-around" or "space-between"
      values = [values[0]+"-"+values[1],values[2]];
    }

    if (values.length > 0) axis.main  = values[0] || axis.main;
    if (values.length > 1) axis.cross = values[1] || axis.cross;

    if (ALIGNMENT_MAIN_AXIS.indexOf(axis.main) < 0)   axis.main = "start";
    if (ALIGNMENT_CROSS_AXIS.indexOf(axis.cross) < 0) axis.cross = "stretch";

    return axis;
  }


})();

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.liveannouncer
 * @description
 * AngularJS Material Live Announcer to provide accessibility for Voice Readers.
 */
MdLiveAnnouncer.$inject = ["$timeout"];
angular
  .module('material.core')
  .service('$mdLiveAnnouncer', MdLiveAnnouncer);

/**
 * @ngdoc service
 * @name $mdLiveAnnouncer
 * @module material.core.liveannouncer
 *
 * @description
 *
 * Service to announce messages to supported screenreaders.
 *
 * > The `$mdLiveAnnouncer` service is internally used for components to provide proper accessibility.
 *
 * <hljs lang="js">
 *   module.controller('AppCtrl', function($mdLiveAnnouncer) {
 *     // Basic announcement (Polite Mode)
 *     $mdLiveAnnouncer.announce('Hey Google');
 *
 *     // Custom announcement (Assertive Mode)
 *     $mdLiveAnnouncer.announce('Hey Google', 'assertive');
 *   });
 * </hljs>
 *
 */
function MdLiveAnnouncer($timeout) {
  /** @private @const @type {!angular.$timeout} */
  this._$timeout = $timeout;

  /** @private @const @type {!HTMLElement} */
  this._liveElement = this._createLiveElement();

  /** @private @const @type {!number} */
  this._announceTimeout = 100;
}

/**
 * @ngdoc method
 * @name $mdLiveAnnouncer#announce
 * @description Announces messages to supported screenreaders.
 * @param {string} message Message to be announced to the screenreader
 * @param {'off'|'polite'|'assertive'} politeness The politeness of the announcer element.
 */
MdLiveAnnouncer.prototype.announce = function(message, politeness) {
  if (!politeness) {
    politeness = 'polite';
  }

  var self = this;

  self._liveElement.textContent = '';
  self._liveElement.setAttribute('aria-live', politeness);

  // This 100ms timeout is necessary for some browser + screen-reader combinations:
  // - Both JAWS and NVDA over IE11 will not announce anything without a non-zero timeout.
  // - With Chrome and IE11 with NVDA or JAWS, a repeated (identical) message won't be read a
  //   second time without clearing and then using a non-zero delay.
  // (using JAWS 17 at time of this writing).
  self._$timeout(function() {
    self._liveElement.textContent = message;
  }, self._announceTimeout, false);
};

/**
 * Creates a live announcer element, which listens for DOM changes and announces them
 * to the screenreaders.
 * @returns {!HTMLElement}
 * @private
 */
MdLiveAnnouncer.prototype._createLiveElement = function() {
  var liveEl = document.createElement('div');

  liveEl.classList.add('md-visually-hidden');
  liveEl.setAttribute('role', 'status');
  liveEl.setAttribute('aria-atomic', 'true');
  liveEl.setAttribute('aria-live', 'polite');

  document.body.appendChild(liveEl);

  return liveEl;
};

})();
(function(){
"use strict";

/**
 * @ngdoc service
 * @name $$mdMeta
 * @module material.core.meta
 *
 * @description
 *
 * A provider and a service that simplifies meta tags access
 *
 * Note: This is intended only for use with dynamic meta tags such as browser color and title.
 * Tags that are only processed when the page is rendered (such as `charset`, and `http-equiv`)
 * will not work since `$$mdMeta` adds the tags after the page has already been loaded.
 *
 * ```js
 * app.config(function($$mdMetaProvider) {
 *   var removeMeta = $$mdMetaProvider.setMeta('meta-name', 'content');
 *   var metaValue  = $$mdMetaProvider.getMeta('meta-name'); // -> 'content'
 *
 *   removeMeta();
 * });
 *
 * app.controller('myController', function($$mdMeta) {
 *   var removeMeta = $$mdMeta.setMeta('meta-name', 'content');
 *   var metaValue  = $$mdMeta.getMeta('meta-name'); // -> 'content'
 *
 *   removeMeta();
 * });
 * ```
 *
 * @returns {$$mdMeta.$service}
 *
 */
angular.module('material.core.meta', [])
  .provider('$$mdMeta', function () {
    var head = angular.element(document.head);
    var metaElements = {};

    /**
     * Checks if the requested element was written manually and maps it
     *
     * @param {string} name meta tag 'name' attribute value
     * @returns {boolean} returns true if there is an element with the requested name
     */
    function mapExistingElement(name) {
      if (metaElements[name]) {
        return true;
      }

      var element = document.getElementsByName(name)[0];

      if (!element) {
        return false;
      }

      metaElements[name] = angular.element(element);

      return true;
    }

    /**
     * @ngdoc method
     * @name $$mdMeta#setMeta
     *
     * @description
     * Creates meta element with the 'name' and 'content' attributes,
     * if the meta tag is already created than we replace the 'content' value
     *
     * @param {string} name meta tag 'name' attribute value
     * @param {string} content meta tag 'content' attribute value
     * @returns {function} remove function
     *
     */
    function setMeta(name, content) {
      mapExistingElement(name);

      if (!metaElements[name]) {
        var newMeta = angular.element('<meta name="' + name + '" content="' + content + '"/>');
        head.append(newMeta);
        metaElements[name] = newMeta;
      }
      else {
        metaElements[name].attr('content', content);
      }

      return function () {
        metaElements[name].attr('content', '');
        metaElements[name].remove();
        delete metaElements[name];
      };
    }

    /**
     * @ngdoc method
     * @name $$mdMeta#getMeta
     *
     * @description
     * Gets the 'content' attribute value of the wanted meta element
     *
     * @param {string} name meta tag 'name' attribute value
     * @returns {string} content attribute value
     */
    function getMeta(name) {
      if (!mapExistingElement(name)) {
        throw Error('$$mdMeta: could not find a meta tag with the name \'' + name + '\'');
      }

      return metaElements[name].attr('content');
    }

    var module = {
      setMeta: setMeta,
      getMeta: getMeta
    };

    return angular.extend({}, module, {
      $get: function () {
        return module;
      }
    });
  });
})();
(function(){
"use strict";

  /**
   * @ngdoc module
   * @name material.core.componentRegistry
   *
   * @description
   * A component instance registration service.
   * Note: currently this as a private service in the SideNav component.
   */
  ComponentRegistry.$inject = ["$log", "$q"];
  angular.module('material.core')
    .factory('$mdComponentRegistry', ComponentRegistry);

  /*
   * @private
   * @ngdoc factory
   * @name ComponentRegistry
   * @module material.core.componentRegistry
   *
   */
  function ComponentRegistry($log, $q) {

    var self;
    var instances = [];
    var pendings = { };

    return self = {
      /**
       * Used to print an error when an instance for a handle isn't found.
       */
      notFoundError: function(handle, msgContext) {
        $log.error((msgContext || "") + 'No instance found for handle', handle);
      },
      /**
       * Return all registered instances as an array.
       */
      getInstances: function() {
        return instances;
      },

      /**
       * Get a registered instance.
       * @param handle the String handle to look up for a registered instance.
       */
      get: function(handle) {
        if (!isValidID(handle)) return null;

        var i, j, instance;
        for (i = 0, j = instances.length; i < j; i++) {
          instance = instances[i];
          if (instance.$$mdHandle === handle) {
            return instance;
          }
        }
        return null;
      },

      /**
       * Register an instance.
       * @param instance the instance to register
       * @param handle the handle to identify the instance under.
       */
      register: function(instance, handle) {
        if (!handle) return angular.noop;

        instance.$$mdHandle = handle;
        instances.push(instance);
        resolveWhen();

        return deregister;

        /**
         * Remove registration for an instance
         */
        function deregister() {
          var index = instances.indexOf(instance);
          if (index !== -1) {
            instances.splice(index, 1);
          }
        }

        /**
         * Resolve any pending promises for this instance
         */
        function resolveWhen() {
          var dfd = pendings[handle];
          if (dfd) {
            dfd.forEach(function (promise) {
              promise.resolve(instance);
            });
            delete pendings[handle];
          }
        }
      },

      /**
       * Async accessor to registered component instance
       * If not available then a promise is created to notify
       * all listeners when the instance is registered.
       */
      when : function(handle) {
        if (isValidID(handle)) {
          var deferred = $q.defer();
          var instance = self.get(handle);

          if (instance)  {
            deferred.resolve(instance);
          } else {
            if (pendings[handle] === undefined) {
              pendings[handle] = [];
            }
            pendings[handle].push(deferred);
          }

          return deferred.promise;
        }
        return $q.reject("Invalid `md-component-id` value.");
      }

    };

    function isValidID(handle){
      return handle && (handle !== "");
    }

  }

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc service
   * @name $mdButtonInkRipple
   * @module material.core
   *
   * @description
   * Provides ripple effects for md-button.  See $mdInkRipple service for all possible configuration options.
   *
   * @param {object=} scope Scope within the current context
   * @param {object=} element The element the ripple effect should be applied to
   * @param {object=} options (Optional) Configuration options to override the default ripple configuration
   */

  MdButtonInkRipple.$inject = ["$mdInkRipple"];
  angular.module('material.core')
    .factory('$mdButtonInkRipple', MdButtonInkRipple);

  function MdButtonInkRipple($mdInkRipple) {
    return {
      attach: function attachRipple(scope, element, options) {
        options = angular.extend(optionsForElement(element), options);

        return $mdInkRipple.attach(scope, element, options);
      }
    };

    function optionsForElement(element) {
      if (element.hasClass('md-icon-button')) {
        return {
          isMenuItem: element.hasClass('md-menu-item'),
          fitRipple: true,
          center: true
        };
      } else {
        return {
          isMenuItem: element.hasClass('md-menu-item'),
          dimBackground: true
        };
      }
    }
  }
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

    /**
   * @ngdoc service
   * @name $mdCheckboxInkRipple
   * @module material.core
   *
   * @description
   * Provides ripple effects for md-checkbox.  See $mdInkRipple service for all possible configuration options.
   *
   * @param {object=} scope Scope within the current context
   * @param {object=} element The element the ripple effect should be applied to
   * @param {object=} options (Optional) Configuration options to override the defaultripple configuration
   */

  MdCheckboxInkRipple.$inject = ["$mdInkRipple"];
  angular.module('material.core')
    .factory('$mdCheckboxInkRipple', MdCheckboxInkRipple);

  function MdCheckboxInkRipple($mdInkRipple) {
    return {
      attach: attach
    };

    function attach(scope, element, options) {
      return $mdInkRipple.attach(scope, element, angular.extend({
        center: true,
        dimBackground: false,
        fitRipple: true
      }, options));
    }
  }
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc service
   * @name $mdListInkRipple
   * @module material.core
   *
   * @description
   * Provides ripple effects for md-list.  See $mdInkRipple service for all possible configuration options.
   *
   * @param {object=} scope Scope within the current context
   * @param {object=} element The element the ripple effect should be applied to
   * @param {object=} options (Optional) Configuration options to override the defaultripple configuration
   */

  MdListInkRipple.$inject = ["$mdInkRipple"];
  angular.module('material.core')
    .factory('$mdListInkRipple', MdListInkRipple);

  function MdListInkRipple($mdInkRipple) {
    return {
      attach: attach
    };

    function attach(scope, element, options) {
      return $mdInkRipple.attach(scope, element, angular.extend({
        center: false,
        dimBackground: true,
        outline: false,
        rippleSize: 'full'
      }, options));
    }
  }
})();

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.ripple
 * @description
 * Ripple
 */
InkRippleCtrl.$inject = ["$scope", "$element", "rippleOptions", "$window", "$timeout", "$mdUtil", "$mdColorUtil"];
InkRippleDirective.$inject = ["$mdButtonInkRipple", "$mdCheckboxInkRipple"];
angular.module('material.core')
    .provider('$mdInkRipple', InkRippleProvider)
    .directive('mdInkRipple', InkRippleDirective)
    .directive('mdNoInk', attrNoDirective)
    .directive('mdNoBar', attrNoDirective)
    .directive('mdNoStretch', attrNoDirective);

var DURATION = 450;

/**
 * @ngdoc directive
 * @name mdInkRipple
 * @module material.core.ripple
 *
 * @description
 * The `md-ink-ripple` directive allows you to specify the ripple color or if a ripple is allowed.
 *
 * @param {string|boolean} md-ink-ripple A color string `#FF0000` or boolean (`false` or `0`) for
 *  preventing ripple
 *
 * @usage
 * ### String values
 * <hljs lang="html">
 *   <ANY md-ink-ripple="#FF0000">
 *     Ripples in red
 *   </ANY>
 *
 *   <ANY md-ink-ripple="false">
 *     Not rippling
 *   </ANY>
 * </hljs>
 *
 * ### Interpolated values
 * <hljs lang="html">
 *   <ANY md-ink-ripple="{{ randomColor() }}">
 *     Ripples with the return value of 'randomColor' function
 *   </ANY>
 *
 *   <ANY md-ink-ripple="{{ canRipple() }}">
 *     Ripples if 'canRipple' function return value is not 'false' or '0'
 *   </ANY>
 * </hljs>
 */
function InkRippleDirective ($mdButtonInkRipple, $mdCheckboxInkRipple) {
  return {
    controller: angular.noop,
    link:       function (scope, element, attr) {
      attr.hasOwnProperty('mdInkRippleCheckbox')
          ? $mdCheckboxInkRipple.attach(scope, element)
          : $mdButtonInkRipple.attach(scope, element);
    }
  };
}

/**
 * @ngdoc service
 * @name $mdInkRipple
 * @module material.core.ripple
 *
 * @description
 * `$mdInkRipple` is a service for adding ripples to any element.
 *
 * @usage
 * <hljs lang="js">
 * app.factory('$myElementInkRipple', function($mdInkRipple) {
 *   return {
 *     attach: function (scope, element, options) {
 *       return $mdInkRipple.attach(scope, element, angular.extend({
 *         center: false,
 *         dimBackground: true
 *       }, options));
 *     }
 *   };
 * });
 *
 * app.controller('myController', function ($scope, $element, $myElementInkRipple) {
 *   $scope.onClick = function (ev) {
 *     $myElementInkRipple.attach($scope, angular.element(ev.target), { center: true });
 *   }
 * });
 * </hljs>
 */

/**
 * @ngdoc service
 * @name $mdInkRippleProvider
 * @module material.core.ripple
 *
 * @description
  * If you want to disable ink ripples globally, for all components, you can call the
 * `disableInkRipple` method in your app's config.
 *
 *
 * @usage
 * <hljs lang="js">
 * app.config(function ($mdInkRippleProvider) {
 *   $mdInkRippleProvider.disableInkRipple();
 * });
 * </hljs>
 */

function InkRippleProvider () {
  var isDisabledGlobally = false;

  return {
    disableInkRipple: disableInkRipple,
    $get: ["$injector", function($injector) {
      return { attach: attach };

      /**
       * @ngdoc method
       * @name $mdInkRipple#attach
       *
       * @description
       * Attaching given scope, element and options to inkRipple controller
       *
       * @param {object=} scope Scope within the current context
       * @param {object=} element The element the ripple effect should be applied to
       * @param {object=} options (Optional) Configuration options to override the defaultRipple configuration
       * * `center` -  Whether the ripple should start from the center of the container element
       * * `dimBackground` - Whether the background should be dimmed with the ripple color
       * * `colorElement` - The element the ripple should take its color from, defined by css property `color`
       * * `fitRipple` - Whether the ripple should fill the element
       */
      function attach (scope, element, options) {
        if (isDisabledGlobally || element.controller('mdNoInk')) return angular.noop;
        return $injector.instantiate(InkRippleCtrl, {
          $scope:        scope,
          $element:      element,
          rippleOptions: options
        });
      }
    }]
  };

  /**
   * @ngdoc method
   * @name $mdInkRippleProvider#disableInkRipple
   *
   * @description
   * A config-time method that, when called, disables ripples globally.
   */
  function disableInkRipple () {
    isDisabledGlobally = true;
  }
}

/**
 * Controller used by the ripple service in order to apply ripples
 * @ngInject
 */
function InkRippleCtrl ($scope, $element, rippleOptions, $window, $timeout, $mdUtil, $mdColorUtil) {
  this.$window    = $window;
  this.$timeout   = $timeout;
  this.$mdUtil    = $mdUtil;
  this.$mdColorUtil    = $mdColorUtil;
  this.$scope     = $scope;
  this.$element   = $element;
  this.options    = rippleOptions;
  this.mousedown  = false;
  this.ripples    = [];
  this.timeout    = null; // Stores a reference to the most-recent ripple timeout
  this.lastRipple = null;

  $mdUtil.valueOnUse(this, 'container', this.createContainer);

  this.$element.addClass('md-ink-ripple');

  // attach method for unit tests
  ($element.controller('mdInkRipple') || {}).createRipple = angular.bind(this, this.createRipple);
  ($element.controller('mdInkRipple') || {}).setColor = angular.bind(this, this.color);

  this.bindEvents();
}


/**
 * Either remove or unlock any remaining ripples when the user mouses off of the element (either by
 * mouseup or mouseleave event)
 */
function autoCleanup (self, cleanupFn) {
  if (self.mousedown || self.lastRipple) {
    self.mousedown = false;
    self.$mdUtil.nextTick(angular.bind(self, cleanupFn), false);
  }
}


/**
 * Returns the color that the ripple should be (either based on CSS or hard-coded)
 * @returns {string}
 */
InkRippleCtrl.prototype.color = function (value) {
  var self = this;

  // If assigning a color value, apply it to background and the ripple color
  if (angular.isDefined(value)) {
    self._color = self._parseColor(value);
  }

  // If color lookup, use assigned, defined, or inherited
  return self._color || self._parseColor(self.inkRipple()) || self._parseColor(getElementColor());

  /**
   * Finds the color element and returns its text color for use as default ripple color
   * @returns {string}
   */
  function getElementColor () {
    var items = self.options && self.options.colorElement ? self.options.colorElement : [];
    var elem =  items.length ? items[ 0 ] : self.$element[ 0 ];

    return elem ? self.$window.getComputedStyle(elem).color : 'rgb(0,0,0)';
  }
};

/**
 * Updating the ripple colors based on the current inkRipple value
 * or the element's computed style color
 */
InkRippleCtrl.prototype.calculateColor = function () {
  return this.color();
};


/**
 * Takes a string color and converts it to RGBA format
 * @param {string} color
 * @param {number} multiplier
 * @returns {string}
 */
InkRippleCtrl.prototype._parseColor = function parseColor (color, multiplier) {
  multiplier = multiplier || 1;
  var colorUtil = this.$mdColorUtil;

  if (!color) return;
  if (color.indexOf('rgba') === 0) return color.replace(/\d?\.?\d*\s*\)\s*$/, (0.1 * multiplier).toString() + ')');
  if (color.indexOf('rgb') === 0) return colorUtil.rgbToRgba(color);
  if (color.indexOf('#') === 0) return colorUtil.hexToRgba(color);

};

/**
 * Binds events to the root element for
 */
InkRippleCtrl.prototype.bindEvents = function () {
  this.$element.on('mousedown', angular.bind(this, this.handleMousedown));
  this.$element.on('mouseup touchend', angular.bind(this, this.handleMouseup));
  this.$element.on('mouseleave', angular.bind(this, this.handleMouseup));
  this.$element.on('touchmove', angular.bind(this, this.handleTouchmove));
};

/**
 * Create a new ripple on every mousedown event from the root element
 * @param event {MouseEvent}
 */
InkRippleCtrl.prototype.handleMousedown = function (event) {
  if (this.mousedown) return;

  // When jQuery is loaded, we have to get the original event
  if (event.hasOwnProperty('originalEvent')) event = event.originalEvent;
  this.mousedown = true;
  if (this.options.center) {
    this.createRipple(this.container.prop('clientWidth') / 2, this.container.prop('clientWidth') / 2);
  } else {

    // We need to calculate the relative coordinates if the target is a sublayer of the ripple element
    if (event.srcElement !== this.$element[0]) {
      var layerRect = this.$element[0].getBoundingClientRect();
      var layerX = event.clientX - layerRect.left;
      var layerY = event.clientY - layerRect.top;

      this.createRipple(layerX, layerY);
    } else {
      this.createRipple(event.offsetX, event.offsetY);
    }
  }
};

/**
 * Either remove or unlock any remaining ripples when the user mouses off of the element (either by
 * mouseup, touchend or mouseleave event)
 */
InkRippleCtrl.prototype.handleMouseup = function () {
  this.$timeout(function () {
    autoCleanup(this, this.clearRipples);
  }.bind(this));
};

/**
 * Either remove or unlock any remaining ripples when the user mouses off of the element (by
 * touchmove)
 */
InkRippleCtrl.prototype.handleTouchmove = function () {
  autoCleanup(this, this.deleteRipples);
};

/**
 * Cycles through all ripples and attempts to remove them.
 */
InkRippleCtrl.prototype.deleteRipples = function () {
  for (var i = 0; i < this.ripples.length; i++) {
    this.ripples[ i ].remove();
  }
};

/**
 * Cycles through all ripples and attempts to remove them with fade.
 * Depending on logic within `fadeInComplete`, some removals will be postponed.
 */
InkRippleCtrl.prototype.clearRipples = function () {
  for (var i = 0; i < this.ripples.length; i++) {
    this.fadeInComplete(this.ripples[ i ]);
  }
};

/**
 * Creates the ripple container element
 * @returns {*}
 */
InkRippleCtrl.prototype.createContainer = function () {
  var container = angular.element('<div class="md-ripple-container"></div>');
  this.$element.append(container);
  return container;
};

InkRippleCtrl.prototype.clearTimeout = function () {
  if (this.timeout) {
    this.$timeout.cancel(this.timeout);
    this.timeout = null;
  }
};

InkRippleCtrl.prototype.isRippleAllowed = function () {
  var element = this.$element[0];
  do {
    if (!element.tagName || element.tagName === 'BODY') break;

    if (element && angular.isFunction(element.hasAttribute)) {
      if (element.hasAttribute('disabled')) return false;
      if (this.inkRipple() === 'false' || this.inkRipple() === '0') return false;
    }

  } while (element = element.parentNode);
  return true;
};

/**
 * The attribute `md-ink-ripple` may be a static or interpolated
 * color value OR a boolean indicator (used to disable ripples)
 */
InkRippleCtrl.prototype.inkRipple = function () {
  return this.$element.attr('md-ink-ripple');
};

/**
 * Creates a new ripple and adds it to the container.  Also tracks ripple in `this.ripples`.
 * @param left
 * @param top
 */
InkRippleCtrl.prototype.createRipple = function (left, top) {
  if (!this.isRippleAllowed()) return;

  var ctrl        = this;
  var colorUtil   = ctrl.$mdColorUtil;
  var ripple      = angular.element('<div class="md-ripple"></div>');
  var width       = this.$element.prop('clientWidth');
  var height      = this.$element.prop('clientHeight');
  var x           = Math.max(Math.abs(width - left), left) * 2;
  var y           = Math.max(Math.abs(height - top), top) * 2;
  var size        = getSize(this.options.fitRipple, x, y);
  var color       = this.calculateColor();

  ripple.css({
    left:            left + 'px',
    top:             top + 'px',
    background:      'black',
    width:           size + 'px',
    height:          size + 'px',
    backgroundColor: colorUtil.rgbaToRgb(color),
    borderColor:     colorUtil.rgbaToRgb(color)
  });
  this.lastRipple = ripple;

  // we only want one timeout to be running at a time
  this.clearTimeout();
  this.timeout    = this.$timeout(function () {
    ctrl.clearTimeout();
    if (!ctrl.mousedown) ctrl.fadeInComplete(ripple);
  }, DURATION * 0.35, false);

  if (this.options.dimBackground) this.container.css({ backgroundColor: color });
  this.container.append(ripple);
  this.ripples.push(ripple);
  ripple.addClass('md-ripple-placed');

  this.$mdUtil.nextTick(function () {

    ripple.addClass('md-ripple-scaled md-ripple-active');
    ctrl.$timeout(function () {
      ctrl.clearRipples();
    }, DURATION, false);

  }, false);

  function getSize (fit, x, y) {
    return fit
        ? Math.max(x, y)
        : Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));
  }
};



/**
 * After fadeIn finishes, either kicks off the fade-out animation or queues the element for removal on mouseup
 * @param ripple
 */
InkRippleCtrl.prototype.fadeInComplete = function (ripple) {
  if (this.lastRipple === ripple) {
    if (!this.timeout && !this.mousedown) {
      this.removeRipple(ripple);
    }
  } else {
    this.removeRipple(ripple);
  }
};

/**
 * Kicks off the animation for removing a ripple
 * @param ripple {Element}
 */
InkRippleCtrl.prototype.removeRipple = function (ripple) {
  var ctrl  = this;
  var index = this.ripples.indexOf(ripple);
  if (index < 0) return;
  this.ripples.splice(this.ripples.indexOf(ripple), 1);
  ripple.removeClass('md-ripple-active');
  ripple.addClass('md-ripple-remove');
  if (this.ripples.length === 0) this.container.css({ backgroundColor: '' });
  // use a 2-second timeout in order to allow for the animation to finish
  // we don't actually care how long the animation takes
  this.$timeout(function () {
    ctrl.fadeOutComplete(ripple);
  }, DURATION, false);
};

/**
 * Removes the provided ripple from the DOM
 * @param ripple
 */
InkRippleCtrl.prototype.fadeOutComplete = function (ripple) {
  ripple.remove();
  this.lastRipple = null;
};

/**
 * Used to create an empty directive.  This is used to track flag-directives whose children may have
 * functionality based on them.
 *
 * Example: `md-no-ink` will potentially be used by all child directives.
 */
function attrNoDirective () {
  return { controller: angular.noop };
}

})();
(function(){
"use strict";

(function() {
  'use strict';

    /**
   * @ngdoc service
   * @name $mdTabInkRipple
   * @module material.core
   *
   * @description
   * Provides ripple effects for md-tabs.  See $mdInkRipple service for all possible configuration options.
   *
   * @param {object=} scope Scope within the current context
   * @param {object=} element The element the ripple effect should be applied to
   * @param {object=} options (Optional) Configuration options to override the defaultripple configuration
   */

  MdTabInkRipple.$inject = ["$mdInkRipple"];
  angular.module('material.core')
    .factory('$mdTabInkRipple', MdTabInkRipple);

  function MdTabInkRipple($mdInkRipple) {
    return {
      attach: attach
    };

    function attach(scope, element, options) {
      return $mdInkRipple.attach(scope, element, angular.extend({
        center: false,
        dimBackground: true,
        outline: false,
        rippleSize: 'full'
      }, options));
    }
  }
})();

})();
(function(){
"use strict";

angular.module('material.core.theming.palette', [])
.constant('$mdColorPalette', {
  'red': {
    '50': '#ffebee',
    '100': '#ffcdd2',
    '200': '#ef9a9a',
    '300': '#e57373',
    '400': '#ef5350',
    '500': '#f44336',
    '600': '#e53935',
    '700': '#d32f2f',
    '800': '#c62828',
    '900': '#b71c1c',
    'A100': '#ff8a80',
    'A200': '#ff5252',
    'A400': '#ff1744',
    'A700': '#d50000',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 300 A100',
    'contrastStrongLightColors': '400 500 600 700 A200 A400 A700'
  },
  'pink': {
    '50': '#fce4ec',
    '100': '#f8bbd0',
    '200': '#f48fb1',
    '300': '#f06292',
    '400': '#ec407a',
    '500': '#e91e63',
    '600': '#d81b60',
    '700': '#c2185b',
    '800': '#ad1457',
    '900': '#880e4f',
    'A100': '#ff80ab',
    'A200': '#ff4081',
    'A400': '#f50057',
    'A700': '#c51162',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 A100',
    'contrastStrongLightColors': '500 600 A200 A400 A700'
  },
  'purple': {
    '50': '#f3e5f5',
    '100': '#e1bee7',
    '200': '#ce93d8',
    '300': '#ba68c8',
    '400': '#ab47bc',
    '500': '#9c27b0',
    '600': '#8e24aa',
    '700': '#7b1fa2',
    '800': '#6a1b9a',
    '900': '#4a148c',
    'A100': '#ea80fc',
    'A200': '#e040fb',
    'A400': '#d500f9',
    'A700': '#aa00ff',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 A100',
    'contrastStrongLightColors': '300 400 A200 A400 A700'
  },
  'deep-purple': {
    '50': '#ede7f6',
    '100': '#d1c4e9',
    '200': '#b39ddb',
    '300': '#9575cd',
    '400': '#7e57c2',
    '500': '#673ab7',
    '600': '#5e35b1',
    '700': '#512da8',
    '800': '#4527a0',
    '900': '#311b92',
    'A100': '#b388ff',
    'A200': '#7c4dff',
    'A400': '#651fff',
    'A700': '#6200ea',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 A100',
    'contrastStrongLightColors': '300 400 A200'
  },
  'indigo': {
    '50': '#e8eaf6',
    '100': '#c5cae9',
    '200': '#9fa8da',
    '300': '#7986cb',
    '400': '#5c6bc0',
    '500': '#3f51b5',
    '600': '#3949ab',
    '700': '#303f9f',
    '800': '#283593',
    '900': '#1a237e',
    'A100': '#8c9eff',
    'A200': '#536dfe',
    'A400': '#3d5afe',
    'A700': '#304ffe',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 A100',
    'contrastStrongLightColors': '300 400 A200 A400'
  },
  'blue': {
    '50': '#e3f2fd',
    '100': '#bbdefb',
    '200': '#90caf9',
    '300': '#64b5f6',
    '400': '#42a5f5',
    '500': '#2196f3',
    '600': '#1e88e5',
    '700': '#1976d2',
    '800': '#1565c0',
    '900': '#0d47a1',
    'A100': '#82b1ff',
    'A200': '#448aff',
    'A400': '#2979ff',
    'A700': '#2962ff',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 300 400 A100',
    'contrastStrongLightColors': '500 600 700 A200 A400 A700'
  },
  'light-blue': {
    '50': '#e1f5fe',
    '100': '#b3e5fc',
    '200': '#81d4fa',
    '300': '#4fc3f7',
    '400': '#29b6f6',
    '500': '#03a9f4',
    '600': '#039be5',
    '700': '#0288d1',
    '800': '#0277bd',
    '900': '#01579b',
    'A100': '#80d8ff',
    'A200': '#40c4ff',
    'A400': '#00b0ff',
    'A700': '#0091ea',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '600 700 800 900 A700',
    'contrastStrongLightColors': '600 700 800 A700'
  },
  'cyan': {
    '50': '#e0f7fa',
    '100': '#b2ebf2',
    '200': '#80deea',
    '300': '#4dd0e1',
    '400': '#26c6da',
    '500': '#00bcd4',
    '600': '#00acc1',
    '700': '#0097a7',
    '800': '#00838f',
    '900': '#006064',
    'A100': '#84ffff',
    'A200': '#18ffff',
    'A400': '#00e5ff',
    'A700': '#00b8d4',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '700 800 900',
    'contrastStrongLightColors': '700 800 900'
  },
  'teal': {
    '50': '#e0f2f1',
    '100': '#b2dfdb',
    '200': '#80cbc4',
    '300': '#4db6ac',
    '400': '#26a69a',
    '500': '#009688',
    '600': '#00897b',
    '700': '#00796b',
    '800': '#00695c',
    '900': '#004d40',
    'A100': '#a7ffeb',
    'A200': '#64ffda',
    'A400': '#1de9b6',
    'A700': '#00bfa5',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '500 600 700 800 900',
    'contrastStrongLightColors': '500 600 700'
  },
  'green': {
    '50': '#e8f5e9',
    '100': '#c8e6c9',
    '200': '#a5d6a7',
    '300': '#81c784',
    '400': '#66bb6a',
    '500': '#4caf50',
    '600': '#43a047',
    '700': '#388e3c',
    '800': '#2e7d32',
    '900': '#1b5e20',
    'A100': '#b9f6ca',
    'A200': '#69f0ae',
    'A400': '#00e676',
    'A700': '#00c853',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '500 600 700 800 900',
    'contrastStrongLightColors': '500 600 700'
  },
  'light-green': {
    '50': '#f1f8e9',
    '100': '#dcedc8',
    '200': '#c5e1a5',
    '300': '#aed581',
    '400': '#9ccc65',
    '500': '#8bc34a',
    '600': '#7cb342',
    '700': '#689f38',
    '800': '#558b2f',
    '900': '#33691e',
    'A100': '#ccff90',
    'A200': '#b2ff59',
    'A400': '#76ff03',
    'A700': '#64dd17',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '700 800 900',
    'contrastStrongLightColors': '700 800 900'
  },
  'lime': {
    '50': '#f9fbe7',
    '100': '#f0f4c3',
    '200': '#e6ee9c',
    '300': '#dce775',
    '400': '#d4e157',
    '500': '#cddc39',
    '600': '#c0ca33',
    '700': '#afb42b',
    '800': '#9e9d24',
    '900': '#827717',
    'A100': '#f4ff81',
    'A200': '#eeff41',
    'A400': '#c6ff00',
    'A700': '#aeea00',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '900',
    'contrastStrongLightColors': '900'
  },
  'yellow': {
    '50': '#fffde7',
    '100': '#fff9c4',
    '200': '#fff59d',
    '300': '#fff176',
    '400': '#ffee58',
    '500': '#ffeb3b',
    '600': '#fdd835',
    '700': '#fbc02d',
    '800': '#f9a825',
    '900': '#f57f17',
    'A100': '#ffff8d',
    'A200': '#ffff00',
    'A400': '#ffea00',
    'A700': '#ffd600',
    'contrastDefaultColor': 'dark'
  },
  'amber': {
    '50': '#fff8e1',
    '100': '#ffecb3',
    '200': '#ffe082',
    '300': '#ffd54f',
    '400': '#ffca28',
    '500': '#ffc107',
    '600': '#ffb300',
    '700': '#ffa000',
    '800': '#ff8f00',
    '900': '#ff6f00',
    'A100': '#ffe57f',
    'A200': '#ffd740',
    'A400': '#ffc400',
    'A700': '#ffab00',
    'contrastDefaultColor': 'dark'
  },
  'orange': {
    '50': '#fff3e0',
    '100': '#ffe0b2',
    '200': '#ffcc80',
    '300': '#ffb74d',
    '400': '#ffa726',
    '500': '#ff9800',
    '600': '#fb8c00',
    '700': '#f57c00',
    '800': '#ef6c00',
    '900': '#e65100',
    'A100': '#ffd180',
    'A200': '#ffab40',
    'A400': '#ff9100',
    'A700': '#ff6d00',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '800 900',
    'contrastStrongLightColors': '800 900'
  },
  'deep-orange': {
    '50': '#fbe9e7',
    '100': '#ffccbc',
    '200': '#ffab91',
    '300': '#ff8a65',
    '400': '#ff7043',
    '500': '#ff5722',
    '600': '#f4511e',
    '700': '#e64a19',
    '800': '#d84315',
    '900': '#bf360c',
    'A100': '#ff9e80',
    'A200': '#ff6e40',
    'A400': '#ff3d00',
    'A700': '#dd2c00',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 300 400 A100 A200',
    'contrastStrongLightColors': '500 600 700 800 900 A400 A700'
  },
  'brown': {
    '50': '#efebe9',
    '100': '#d7ccc8',
    '200': '#bcaaa4',
    '300': '#a1887f',
    '400': '#8d6e63',
    '500': '#795548',
    '600': '#6d4c41',
    '700': '#5d4037',
    '800': '#4e342e',
    '900': '#3e2723',
    'A100': '#d7ccc8',
    'A200': '#bcaaa4',
    'A400': '#8d6e63',
    'A700': '#5d4037',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 A100 A200',
    'contrastStrongLightColors': '300 400'
  },
  'grey': {
    '50': '#fafafa',
    '100': '#f5f5f5',
    '200': '#eeeeee',
    '300': '#e0e0e0',
    '400': '#bdbdbd',
    '500': '#9e9e9e',
    '600': '#757575',
    '700': '#616161',
    '800': '#424242',
    '900': '#212121',
    'A100': '#ffffff',
    'A200': '#000000',
    'A400': '#303030',
    'A700': '#616161',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '600 700 800 900 A200 A400 A700'
  },
  'blue-grey': {
    '50': '#eceff1',
    '100': '#cfd8dc',
    '200': '#b0bec5',
    '300': '#90a4ae',
    '400': '#78909c',
    '500': '#607d8b',
    '600': '#546e7a',
    '700': '#455a64',
    '800': '#37474f',
    '900': '#263238',
    'A100': '#cfd8dc',
    'A200': '#b0bec5',
    'A400': '#78909c',
    'A700': '#455a64',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 300 A100 A200',
    'contrastStrongLightColors': '400 500 700'
  }
});

})();
(function(){
"use strict";

(function(angular) {
  'use strict';
/**
 * @ngdoc module
 * @name material.core.theming
 * @description
 * Theming
 */
detectDisabledThemes.$inject = ["$mdThemingProvider"];
ThemingDirective.$inject = ["$mdTheming", "$interpolate", "$parse", "$mdUtil", "$q", "$log"];
ThemableDirective.$inject = ["$mdTheming"];
ThemingProvider.$inject = ["$mdColorPalette", "$$mdMetaProvider"];
generateAllThemes.$inject = ["$injector", "$mdTheming"];
angular.module('material.core.theming', ['material.core.theming.palette', 'material.core.meta'])
  .directive('mdTheme', ThemingDirective)
  .directive('mdThemable', ThemableDirective)
  .directive('mdThemesDisabled', disableThemesDirective)
  .provider('$mdTheming', ThemingProvider)
  .config(detectDisabledThemes)
  .run(generateAllThemes);

/**
 * Detect if the HTML or the BODY tags has a [md-themes-disabled] attribute
 * If yes, then immediately disable all theme stylesheet generation and DOM injection
 */
/**
 * @ngInject
 */
function detectDisabledThemes($mdThemingProvider) {
  var isDisabled = !!document.querySelector('[md-themes-disabled]');
  $mdThemingProvider.disableTheming(isDisabled);
}

/**
 * @ngdoc service
 * @name $mdThemingProvider
 * @module material.core.theming
 *
 * @description Provider to configure the `$mdTheming` service.
 *
 * ### Default Theme
 * The `$mdThemingProvider` uses by default the following theme configuration:
 *
 * - Primary Palette: `Blue`
 * - Accent Palette: `Pink`
 * - Warn Palette: `Deep-Orange`
 * - Background Palette: `Grey`
 *
 * If you don't want to use the `md-theme` directive on the elements itself, you may want to overwrite
 * the default theme.<br/>
 * This can be done by using the following markup.
 *
 * <hljs lang="js">
 *   myAppModule.config(function($mdThemingProvider) {
 *     $mdThemingProvider
 *       .theme('default')
 *       .primaryPalette('blue')
 *       .accentPalette('teal')
 *       .warnPalette('red')
 *       .backgroundPalette('grey');
 *   });
 * </hljs>
 *

 * ### Dynamic Themes
 *
 * By default, if you change a theme at runtime, the `$mdTheming` service will not detect those changes.<br/>
 * If you have an application, which changes its theme on runtime, you have to enable theme watching.
 *
 * <hljs lang="js">
 *   myAppModule.config(function($mdThemingProvider) {
 *     // Enable theme watching.
 *     $mdThemingProvider.alwaysWatchTheme(true);
 *   });
 * </hljs>
 *
 * ### Custom Theme Styles
 *
 * Sometimes you may want to use your own theme styles for some custom components.<br/>
 * You are able to register your own styles by using the following markup.
 *
 * <hljs lang="js">
 *   myAppModule.config(function($mdThemingProvider) {
 *     // Register our custom stylesheet into the theming provider.
 *     $mdThemingProvider.registerStyles(STYLESHEET);
 *   });
 * </hljs>
 *
 * The `registerStyles` method only accepts strings as value, so you're actually not able to load an external
 * stylesheet file into the `$mdThemingProvider`.
 *
 * If it's necessary to load an external stylesheet, we suggest using a bundler, which supports including raw content,
 * like [raw-loader](https://github.com/webpack/raw-loader) for `webpack`.
 *
 * <hljs lang="js">
 *   myAppModule.config(function($mdThemingProvider) {
 *     // Register your custom stylesheet into the theming provider.
 *     $mdThemingProvider.registerStyles(require('../styles/my-component.theme.css'));
 *   });
 * </hljs>
 *
 * ### Browser color
 *
 * Enables browser header coloring
 * for more info please visit:
 * https://developers.google.com/web/fundamentals/design-and-ui/browser-customization/theme-color
 *
 * Options parameter: <br/>
 * `theme`   - A defined theme via `$mdThemeProvider` to use the palettes from. Default is `default` theme. <br/>
 * `palette` - Can be any one of the basic material design palettes, extended defined palettes and 'primary',
 *             'accent', 'background' and 'warn'. Default is `primary`. <br/>
 * `hue`     - The hue from the selected palette. Default is `800`<br/>
 *
 * <hljs lang="js">
 *   myAppModule.config(function($mdThemingProvider) {
 *     // Enable browser color
 *     $mdThemingProvider.enableBrowserColor({
 *       theme: 'myTheme', // Default is 'default'
 *       palette: 'accent', // Default is 'primary', any basic material palette and extended palettes are available
 *       hue: '200' // Default is '800'
 *     });
 *   });
 * </hljs>
 */

/**
 * Some Example Valid Theming Expressions
 * =======================================
 *
 * Intention group expansion: (valid for primary, accent, warn, background)
 *
 * {{primary-100}} - grab shade 100 from the primary palette
 * {{primary-100-0.7}} - grab shade 100, apply opacity of 0.7
 * {{primary-100-contrast}} - grab shade 100's contrast color
 * {{primary-hue-1}} - grab the shade assigned to hue-1 from the primary palette
 * {{primary-hue-1-0.7}} - apply 0.7 opacity to primary-hue-1
 * {{primary-color}} - Generates .md-hue-1, .md-hue-2, .md-hue-3 with configured shades set for each hue
 * {{primary-color-0.7}} - Apply 0.7 opacity to each of the above rules
 * {{primary-contrast}} - Generates .md-hue-1, .md-hue-2, .md-hue-3 with configured contrast (ie. text) color shades set for each hue
 * {{primary-contrast-0.7}} - Apply 0.7 opacity to each of the above rules
 *
 * Foreground expansion: Applies rgba to black/white foreground text
 *
 * {{foreground-1}} - used for primary text
 * {{foreground-2}} - used for secondary text/divider
 * {{foreground-3}} - used for disabled text
 * {{foreground-4}} - used for dividers
 */

// In memory generated CSS rules; registered by theme.name
var GENERATED = { };

// In memory storage of defined themes and color palettes (both loaded by CSS, and user specified)
var PALETTES;

// Text Colors on light and dark backgrounds
// @see https://material.io/archive/guidelines/style/color.html#color-usability
var DARK_FOREGROUND = {
  name: 'dark',
  '1': 'rgba(0,0,0,0.87)',
  '2': 'rgba(0,0,0,0.54)',
  '3': 'rgba(0,0,0,0.38)',
  '4': 'rgba(0,0,0,0.12)'
};
var LIGHT_FOREGROUND = {
  name: 'light',
  '1': 'rgba(255,255,255,1.0)',
  '2': 'rgba(255,255,255,0.7)',
  '3': 'rgba(255,255,255,0.5)',
  '4': 'rgba(255,255,255,0.12)'
};

var DARK_SHADOW = '1px 1px 0px rgba(0,0,0,0.4), -1px -1px 0px rgba(0,0,0,0.4)';
var LIGHT_SHADOW = '';

var DARK_CONTRAST_COLOR = colorToRgbaArray('rgba(0,0,0,0.87)');
var LIGHT_CONTRAST_COLOR = colorToRgbaArray('rgba(255,255,255,0.87)');
var STRONG_LIGHT_CONTRAST_COLOR = colorToRgbaArray('rgb(255,255,255)');

var THEME_COLOR_TYPES = ['primary', 'accent', 'warn', 'background'];
var DEFAULT_COLOR_TYPE = 'primary';

// A color in a theme will use these hues by default, if not specified by user.
var LIGHT_DEFAULT_HUES = {
  'accent': {
    'default': 'A200',
    'hue-1': 'A100',
    'hue-2': 'A400',
    'hue-3': 'A700'
  },
  'background': {
    'default': '50',
    'hue-1': 'A100',
    'hue-2': '100',
    'hue-3': '300'
  }
};

var DARK_DEFAULT_HUES = {
  'background': {
    'default': 'A400',
    'hue-1': '800',
    'hue-2': '900',
    'hue-3': 'A200'
  }
};
THEME_COLOR_TYPES.forEach(function(colorType) {
  // Color types with unspecified default hues will use these default hue values
  var defaultDefaultHues = {
    'default': '500',
    'hue-1': '300',
    'hue-2': '800',
    'hue-3': 'A100'
  };
  if (!LIGHT_DEFAULT_HUES[colorType]) LIGHT_DEFAULT_HUES[colorType] = defaultDefaultHues;
  if (!DARK_DEFAULT_HUES[colorType]) DARK_DEFAULT_HUES[colorType] = defaultDefaultHues;
});

var VALID_HUE_VALUES = [
  '50', '100', '200', '300', '400', '500', '600',
  '700', '800', '900', 'A100', 'A200', 'A400', 'A700'
];

var themeConfig = {
  disableTheming : false,   // Generate our themes at run time; also disable stylesheet DOM injection
  generateOnDemand : false, // Whether or not themes are to be generated on-demand (vs. eagerly).
  registeredStyles : [],    // Custom styles registered to be used in the theming of custom components.
  nonce : null              // Nonce to be added as an attribute to the generated themes style tags.
};

/**
 *
 */
function ThemingProvider($mdColorPalette, $$mdMetaProvider) {
  ThemingService.$inject = ["$rootScope", "$mdUtil", "$q", "$log"];
  PALETTES = { };
  var THEMES = { };

  var themingProvider;

  var alwaysWatchTheme = false;
  var defaultTheme = 'default';

  // Load JS Defined Palettes
  angular.extend(PALETTES, $mdColorPalette);

  // Default theme defined in core.js

  /**
   * Adds `theme-color` and `msapplication-navbutton-color` meta tags with the color parameter
   * @param {string} color Hex value of the wanted browser color
   * @returns {function} Remove function of the meta tags
   */
  var setBrowserColor = function (color) {
    // Chrome, Firefox OS and Opera
    var removeChrome = $$mdMetaProvider.setMeta('theme-color', color);
    // Windows Phone
    var removeWindows = $$mdMetaProvider.setMeta('msapplication-navbutton-color', color);

    return function () {
      removeChrome();
      removeWindows();
    };
  };

  /**
   * @ngdoc method
   * @name $mdThemingProvider#enableBrowserColor
   * @description
   * Enables browser header coloring. For more info please visit
   * <a href="https://developers.google.com/web/fundamentals/design-and-ui/browser-customization/theme-color">
   *   Web Fundamentals</a>.
   * @param {object=} options Options for the browser color, which include:<br/>
   * - `theme` - `{string}`: A defined theme via `$mdThemeProvider` to use the palettes from. Default is `default` theme. <br/>
   * - `palette` - `{string}`:  Can be any one of the basic material design palettes, extended defined palettes, or `primary`,
   *  `accent`, `background`, and `warn`. Default is `primary`.<br/>
   * - `hue` -  `{string}`: The hue from the selected palette. Default is `800`.<br/>
   * @returns {function} Function that removes the browser coloring when called.
   */
  var enableBrowserColor = function (options) {
    options = angular.isObject(options) ? options : {};

    var theme = options.theme || 'default';
    var hue = options.hue || '800';

    var palette = PALETTES[options.palette] ||
      PALETTES[THEMES[theme].colors[options.palette || 'primary'].name];

    var color = angular.isObject(palette[hue]) ? palette[hue].hex : palette[hue];
    if (color.substr(0, 1) !== '#') color = '#' + color;

    return setBrowserColor(color);
  };

  return themingProvider = {
    definePalette: definePalette,
    extendPalette: extendPalette,
    theme: registerTheme,

    /**
     * return a read-only clone of the current theme configuration
     */
    configuration : function() {
      return angular.extend({ }, themeConfig, {
        defaultTheme : defaultTheme,
        alwaysWatchTheme : alwaysWatchTheme,
        registeredStyles : [].concat(themeConfig.registeredStyles)
      });
    },

    /**
     * @ngdoc method
     * @name $mdThemingProvider#disableTheming
     * @description
     * An easier way to disable theming without having to use `.constant("$MD_THEME_CSS","");`.
     * This disables all dynamic theme style sheet generations and injections.
     * @param {boolean=} isDisabled Disable all dynamic theme style sheet generations and injections
     *  if `true` or `undefined`.
     */
    disableTheming: function(isDisabled) {
      themeConfig.disableTheming = angular.isUndefined(isDisabled) || !!isDisabled;
    },

    /**
     * @ngdoc method
     * @name $mdThemingProvider#registerStyles
     * @param {string} styles The styles to be appended to AngularJS Material's built in theme CSS.
     */
    registerStyles: function(styles) {
      themeConfig.registeredStyles.push(styles);
    },

    /**
     * @ngdoc method
     * @name $mdThemingProvider#setNonce
     * @param {string} nonceValue The nonce to be added as an attribute to the theme style tags.
     * Setting a value allows the use of CSP policy without using the `'unsafe-inline'` directive.
     * The string must already be base64 encoded. You can use `btoa(string)` to do this encoding.
     * In your CSP's `style-src`, you would then add an entry for `'nonce-nonceValue'`.
     */
    setNonce: function(nonceValue) {
      themeConfig.nonce = nonceValue;
    },

    generateThemesOnDemand: function(onDemand) {
      themeConfig.generateOnDemand = onDemand;
    },

    /**
     * @ngdoc method
     * @name $mdThemingProvider#setDefaultTheme
     * @param {string} theme Default theme name to be applied to elements. Default value is `default`.
     */
    setDefaultTheme: function(theme) {
      defaultTheme = theme;
    },

    /**
     * @ngdoc method
     * @name $mdThemingProvider#alwaysWatchTheme
     * @param {boolean} alwaysWatch Whether or not to always watch themes for changes and re-apply
     * classes when they change. Default is `false`. Enabling can reduce performance.
     */
    alwaysWatchTheme: function(alwaysWatch) {
      alwaysWatchTheme = alwaysWatch;
    },

    enableBrowserColor: enableBrowserColor,

    $get: ThemingService,
    _LIGHT_DEFAULT_HUES: LIGHT_DEFAULT_HUES,
    _DARK_DEFAULT_HUES: DARK_DEFAULT_HUES,
    _PALETTES: PALETTES,
    _THEMES: THEMES,
    _parseRules: parseRules,
    _rgba: rgba
  };

  /**
   * @ngdoc method
   * @name $mdThemingProvider#definePalette
   * @description
   * In the event that you need to define a custom color palette, you can use this function to
   * make it available to your theme for use in its intention groups.<br>
   * Note that you must specify all hues in the definition map.
   * @param {string} name Name of palette being defined
   * @param {object} map Palette definition that includes hue definitions and contrast colors:
   * - `'50'` - `{string}`: HEX color
   * - `'100'` - `{string}`: HEX color
   * - `'200'` - `{string}`: HEX color
   * - `'300'` - `{string}`: HEX color
   * - `'400'` - `{string}`: HEX color
   * - `'500'` - `{string}`: HEX color
   * - `'600'` - `{string}`: HEX color
   * - `'700'` - `{string}`: HEX color
   * - `'800'` - `{string}`: HEX color
   * - `'900'` - `{string}`: HEX color
   * - `'A100'` - `{string}`: HEX color
   * - `'A200'` - `{string}`: HEX color
   * - `'A400'` - `{string}`: HEX color
   * - `'A700'` - `{string}`: HEX color
   * - `'contrastDefaultColor'` - `{string}`: `light` or `dark`
   * - `'contrastDarkColors'` - `{string[]}`: Hues which should use dark contrast colors (i.e. raised button text).
   *  For example: `['50', '100', '200', '300', '400', 'A100']`.
   * - `'contrastLightColors'` - `{string[]}`: Hues which should use light contrast colors (i.e. raised button text).
   *  For example: `['500', '600', '700', '800', '900', 'A200', 'A400', 'A700']`.
   */
  function definePalette(name, map) {
    map = map || {};
    PALETTES[name] = checkPaletteValid(name, map);
    return themingProvider;
  }

  /**
   * @ngdoc method
   * @name $mdThemingProvider#extendPalette
   * @description
   * Sometimes it is easier to extend an existing color palette and then change a few properties,
   * rather than defining a whole new palette.
   * @param {string} name Name of palette being extended
   * @param {object} map Palette definition that includes optional hue definitions and contrast colors:
   * - `'50'` - `{string}`: HEX color
   * - `'100'` - `{string}`: HEX color
   * - `'200'` - `{string}`: HEX color
   * - `'300'` - `{string}`: HEX color
   * - `'400'` - `{string}`: HEX color
   * - `'500'` - `{string}`: HEX color
   * - `'600'` - `{string}`: HEX color
   * - `'700'` - `{string}`: HEX color
   * - `'800'` - `{string}`: HEX color
   * - `'900'` - `{string}`: HEX color
   * - `'A100'` - `{string}`: HEX color
   * - `'A200'` - `{string}`: HEX color
   * - `'A400'` - `{string}`: HEX color
   * - `'A700'` - `{string}`: HEX color
   * - `'contrastDefaultColor'` - `{string}`: `light` or `dark`
   * - `'contrastDarkColors'` - `{string[]}`: Hues which should use dark contrast colors (i.e. raised button text).
   *  For example: `['50', '100', '200', '300', '400', 'A100']`.
   * - `'contrastLightColors'` - `{string[]}`: Hues which should use light contrast colors (i.e. raised button text).
   *  For example: `['500', '600', '700', '800', '900', 'A200', 'A400', 'A700']`.
   *  @returns {object} A new object which is a copy of the given palette, `name`,
   *    with variables from `map` overwritten.
   */
  function extendPalette(name, map) {
    return checkPaletteValid(name,  angular.extend({}, PALETTES[name] || {}, map));
  }

  // Make sure that palette has all required hues
  function checkPaletteValid(name, map) {
    var missingColors = VALID_HUE_VALUES.filter(function(field) {
      return !map[field];
    });
    if (missingColors.length) {
      throw new Error("Missing colors %1 in palette %2!"
                      .replace('%1', missingColors.join(', '))
                      .replace('%2', name));
    }

    return map;
  }

  /**
   * @ngdoc method
   * @name $mdThemingProvider#theme
   * @description
   * Register a theme (which is a collection of color palettes); i.e. `warn`, `accent`,
   * `background`, and `primary`.<br>
   * Optionally inherit from an existing theme.
   * @param {string} name Name of theme being registered
   * @param {string=} inheritFrom Existing theme name to inherit from
   */
  function registerTheme(name, inheritFrom) {
    if (THEMES[name]) return THEMES[name];

    inheritFrom = inheritFrom || 'default';

    var parentTheme = typeof inheritFrom === 'string' ? THEMES[inheritFrom] : inheritFrom;
    var theme = new Theme(name);

    if (parentTheme) {
      angular.forEach(parentTheme.colors, function(color, colorType) {
        theme.colors[colorType] = {
          name: color.name,
          // Make sure a COPY of the hues is given to the child color,
          // not the same reference.
          hues: angular.extend({}, color.hues)
        };
      });
    }
    THEMES[name] = theme;

    return theme;
  }

  function Theme(name) {
    var self = this;
    self.name = name;
    self.colors = {};

    self.dark = setDark;
    setDark(false);

    function setDark(isDark) {
      isDark = arguments.length === 0 ? true : !!isDark;

      // If no change, abort
      if (isDark === self.isDark) return;

      self.isDark = isDark;

      self.foregroundPalette = self.isDark ? LIGHT_FOREGROUND : DARK_FOREGROUND;
      self.foregroundShadow = self.isDark ? DARK_SHADOW : LIGHT_SHADOW;

      // Light and dark themes have different default hues.
      // Go through each existing color type for this theme, and for every
      // hue value that is still the default hue value from the previous light/dark setting,
      // set it to the default hue value from the new light/dark setting.
      var newDefaultHues = self.isDark ? DARK_DEFAULT_HUES : LIGHT_DEFAULT_HUES;
      var oldDefaultHues = self.isDark ? LIGHT_DEFAULT_HUES : DARK_DEFAULT_HUES;
      angular.forEach(newDefaultHues, function(newDefaults, colorType) {
        var color = self.colors[colorType];
        var oldDefaults = oldDefaultHues[colorType];
        if (color) {
          for (var hueName in color.hues) {
            if (color.hues[hueName] === oldDefaults[hueName]) {
              color.hues[hueName] = newDefaults[hueName];
            }
          }
        }
      });

      return self;
    }

    THEME_COLOR_TYPES.forEach(function(colorType) {
      var defaultHues = (self.isDark ? DARK_DEFAULT_HUES : LIGHT_DEFAULT_HUES)[colorType];
      self[colorType + 'Palette'] = function setPaletteType(paletteName, hues) {
        var color = self.colors[colorType] = {
          name: paletteName,
          hues: angular.extend({}, defaultHues, hues)
        };

        Object.keys(color.hues).forEach(function(name) {
          if (!defaultHues[name]) {
            throw new Error("Invalid hue name '%1' in theme %2's %3 color %4. Available hue names: %4"
              .replace('%1', name)
              .replace('%2', self.name)
              .replace('%3', paletteName)
              .replace('%4', Object.keys(defaultHues).join(', '))
            );
          }
        });
        Object.keys(color.hues).map(function(key) {
          return color.hues[key];
        }).forEach(function(hueValue) {
          if (VALID_HUE_VALUES.indexOf(hueValue) == -1) {
            throw new Error("Invalid hue value '%1' in theme %2's %3 color %4. Available hue values: %5"
              .replace('%1', hueValue)
              .replace('%2', self.name)
              .replace('%3', colorType)
              .replace('%4', paletteName)
              .replace('%5', VALID_HUE_VALUES.join(', '))
            );
          }
        });
        return self;
      };

      self[colorType + 'Color'] = function() {
        var args = Array.prototype.slice.call(arguments);
        // eslint-disable-next-line no-console
        console.warn('$mdThemingProviderTheme.' + colorType + 'Color() has been deprecated. ' +
                     'Use $mdThemingProviderTheme.' + colorType + 'Palette() instead.');
        return self[colorType + 'Palette'].apply(self, args);
      };
    });
  }

  /**
   * @ngdoc service
   * @name $mdTheming
   * @module material.core.theming
   * @description
   * Service that makes an element apply theming related <b>classes</b> to itself.
   *
   * For more information on the hue objects, their default values, as well as valid hue values, please visit <a ng-href="Theming/03_configuring_a_theme#specifying-custom-hues-for-color-intentions">the custom hues section of Configuring a Theme</a>.
   *
   * <hljs lang="js">
   * // Example component directive that we want to apply theming classes to.
   * app.directive('myFancyDirective', function($mdTheming) {
   *   return {
   *     restrict: 'AE',
   *     link: function(scope, element, attrs) {
   *       // Initialize the service using our directive's element
   *       $mdTheming(element);
   *
   *       $mdTheming.defineTheme('myTheme', {
   *         primary: 'blue',
   *         primaryHues: {
   *           default: '500',
   *           hue-1: '300',
   *           hue-2: '900',
   *           hue-3: 'A100'
   *         },
   *         accent: 'pink',
   *         accentHues: {
   *           default: '600',
   *           hue-1: '300',
   *           hue-2: '200',
   *           hue-3: 'A500'
   *         },
   *         warn: 'red',
   *         // It's not necessary to specify all hues in the object.
   *         warnHues: {
   *           default: '200',
   *           hue-3: 'A100'
   *         },
   *         // It's not necessary to specify custom hues at all.
   *         background: 'grey',
   *         dark: true
   *       });
   *       // Your directive's custom code here.
   *     }
   *   };
   * });
   * </hljs>
   * @param {element=} element Element that will have theming classes applied to it.
   */

  /**
   * @ngdoc property
   * @name $mdTheming#THEMES
   * @description
   * Property to get all the themes defined
   * @returns {object} All the themes defined with their properties.
   */

  /**
   * @ngdoc property
   * @name $mdTheming#PALETTES
   * @description
   * Property to get all the palettes defined
   * @returns {object} All the palettes defined with their colors.
   */

  /**
   * @ngdoc method
   * @name $mdTheming#registered
   * @description
   * Determine is specified theme name is a valid, registered theme
   * @param {string} themeName the theme to check if registered
   * @returns {boolean} whether the theme is registered or not
   */

  /**
   * @ngdoc method
   * @name $mdTheming#defaultTheme
   * @description
   * Returns the default theme
   * @returns {string} The default theme
   */

  /**
   * @ngdoc method
   * @name $mdTheming#generateTheme
   * @description
   * Lazy generate themes - by default, every theme is generated when defined.
   * You can disable this in the configuration section using the
   * `$mdThemingProvider.generateThemesOnDemand(true);`
   *
   * The theme name that is passed in must match the name of the theme that was defined as part of
   * the configuration block.
   *
   * @param {string} name theme name to generate
   */

  /**
   * @ngdoc method
   * @name $mdTheming#setBrowserColor
   * @description
   * Enables browser header coloring. For more info please visit
   * <a href="https://developers.google.com/web/fundamentals/design-and-ui/browser-customization/theme-color">
   *   Web Fundamentals</a>.
   * @param {object=} options Options for the browser color, which include:<br/>
   * - `theme` - `{string}`: A defined theme via `$mdThemeProvider` to use the palettes from.
   *    Default is `default` theme. <br/>
   * - `palette` - `{string}`:  Can be any one of the basic material design palettes, extended
   *    defined palettes, or `primary`, `accent`, `background`, and `warn`. Default is `primary`.
   * <br/>
   * - `hue` -  `{string}`: The hue from the selected palette. Default is `800`.<br/>
   * @returns {function} Function that removes the browser coloring when called.
   */

  /**
   * @ngdoc method
   * @name $mdTheming#defineTheme
   * @description
   * Dynamically define a theme by using an options object that contains palette names.
   *
   * @param {string} name Theme name to define
   * @param {object} options Theme definition options
   *
   * Options are:<br/>
   * - `primary` - `{string}`: The name of the primary palette to use in the theme.<br/>
   * - `primaryHues` - `{object=}`: Override hues for primary palette.<br/>
   * - `accent` - `{string}`: The name of the accent palette to use in the theme.<br/>
   * - `accentHues` - `{object=}`: Override hues for accent palette.<br/>
   * - `warn` - `{string}`: The name of the warn palette to use in the theme.<br/>
   * - `warnHues` - `{object=}`: Override hues for warn palette.<br/>
   * - `background` - `{string}`: The name of the background palette to use in the theme.<br/>
   * - `backgroundHues` - `{object=}`: Override hues for background palette.<br/>
   * - `dark` - `{boolean}`: Indicates if it's a dark theme.<br/>
   * @returns {Promise<string>} A resolved promise with the new theme name.
   */

  /* @ngInject */
  function ThemingService($rootScope, $mdUtil, $q, $log) {
    // Allow us to be invoked via a linking function signature.
    var applyTheme = function (scope, el) {
      if (el === undefined) { el = scope; scope = undefined; }
      if (scope === undefined) { scope = $rootScope; }
      applyTheme.inherit(el, el);
    };

    Object.defineProperty(applyTheme, 'THEMES', {
      get: function () {
        return angular.extend({}, THEMES);
      }
    });
    Object.defineProperty(applyTheme, 'PALETTES', {
      get: function () {
        return angular.extend({}, PALETTES);
      }
    });
    Object.defineProperty(applyTheme, 'ALWAYS_WATCH', {
      get: function () {
        return alwaysWatchTheme;
      }
    });
    applyTheme.inherit = inheritTheme;
    applyTheme.registered = registered;
    applyTheme.defaultTheme = function() { return defaultTheme; };
    applyTheme.generateTheme = function(name) { generateTheme(THEMES[name], name, themeConfig.nonce); };
    applyTheme.defineTheme = function(name, options) {
      options = options || {};

      var theme = registerTheme(name);

      if (options.primary) {
        theme.primaryPalette(options.primary, options.primaryHues);
      }
      if (options.accent) {
        theme.accentPalette(options.accent, options.accentHues);
      }
      if (options.warn) {
        theme.warnPalette(options.warn, options.warnHues);
      }
      if (options.background) {
        theme.backgroundPalette(options.background, options.backgroundHues);
      }
      if (options.dark){
        theme.dark();
      }

      this.generateTheme(name);

      return $q.resolve(name);
    };
    applyTheme.setBrowserColor = enableBrowserColor;

    return applyTheme;

    /**
     * Determine is specified theme name is a valid, registered theme
     */
    function registered(themeName) {
      if (themeName === undefined || themeName === '') return true;
      return applyTheme.THEMES[themeName] !== undefined;
    }

    /**
     * Get theme name for the element, then update with Theme CSS class
     */
    function inheritTheme (el, parent) {
      var ctrl = parent.controller('mdTheme') || el.data('$mdThemeController');
      var scope = el.scope();

      updateThemeClass(lookupThemeName());

      if (ctrl) {
        var watchTheme = alwaysWatchTheme ||
                         ctrl.$shouldWatch ||
                         $mdUtil.parseAttributeBoolean(el.attr('md-theme-watch'));

        if (watchTheme || ctrl.isAsyncTheme) {
          var clearNameWatcher = function () {
            if (unwatch) {
              unwatch();
              unwatch = undefined;
            }
          };

          var unwatch = ctrl.registerChanges(function(name) {
            updateThemeClass(name);

            if (!watchTheme) {
              clearNameWatcher();
            }
          });

          if (scope) {
            scope.$on('$destroy', clearNameWatcher);
          } else {
            el.on('$destroy', clearNameWatcher);
          }
        }
      }

      /**
       * Find the theme name from the parent controller or element data
       */
      function lookupThemeName() {
        // As a few components (dialog) add their controllers later, we should also watch for a controller init.
        return ctrl && ctrl.$mdTheme || (defaultTheme === 'default' ? '' : defaultTheme);
      }

      /**
       * Remove old theme class and apply a new one
       * NOTE: if not a valid theme name, then the current name is not changed
       */
      function updateThemeClass(theme) {
        if (!theme) return;
        if (!registered(theme)) {
          $log.warn('Attempted to use unregistered theme \'' + theme + '\'. ' +
                    'Register it with $mdThemingProvider.theme().');
        }

        var oldTheme = el.data('$mdThemeName');
        if (oldTheme) el.removeClass('md-' + oldTheme +'-theme');
        el.addClass('md-' + theme + '-theme');
        el.data('$mdThemeName', theme);
        if (ctrl) {
          el.data('$mdThemeController', ctrl);
        }
      }
    }

  }
}

function ThemingDirective($mdTheming, $interpolate, $parse, $mdUtil, $q, $log) {
  return {
    priority: 101, // has to be more than 100 to be before interpolation (issue on IE)
    link: {
      pre: function(scope, el, attrs) {
        var registeredCallbacks = [];

        var startSymbol = $interpolate.startSymbol();
        var endSymbol = $interpolate.endSymbol();

        var theme = attrs.mdTheme.trim();

        var hasInterpolation =
          theme.substr(0, startSymbol.length) === startSymbol &&
          theme.lastIndexOf(endSymbol) === theme.length - endSymbol.length;

        var oneTimeOperator = '::';
        var oneTimeBind = attrs.mdTheme
            .split(startSymbol).join('')
            .split(endSymbol).join('')
            .trim()
            .substr(0, oneTimeOperator.length) === oneTimeOperator;

        var getTheme = function () {
          var interpolation = $interpolate(attrs.mdTheme)(scope);
          return $parse(interpolation)(scope) || interpolation;
        };

        var ctrl = {
          isAsyncTheme: angular.isFunction(getTheme()) || angular.isFunction(getTheme().then),
          registerChanges: function (cb, context) {
            if (context) {
              cb = angular.bind(context, cb);
            }

            registeredCallbacks.push(cb);

            return function () {
              var index = registeredCallbacks.indexOf(cb);

              if (index > -1) {
                registeredCallbacks.splice(index, 1);
              }
            };
          },
          $setTheme: function (theme) {
            if (!$mdTheming.registered(theme)) {
              $log.warn('attempted to use unregistered theme \'' + theme + '\'');
            }

            ctrl.$mdTheme = theme;

            // Iterating backwards to support unregistering during iteration
            // http://stackoverflow.com/a/9882349/890293
            // we don't use `reverse()` of array because it mutates the array and we don't want it
            // to get re-indexed
            for (var i = registeredCallbacks.length; i--;) {
              registeredCallbacks[i](theme);
            }
          },
          $shouldWatch: $mdUtil.parseAttributeBoolean(el.attr('md-theme-watch')) ||
                        $mdTheming.ALWAYS_WATCH ||
                        (hasInterpolation && !oneTimeBind)
        };

        el.data('$mdThemeController', ctrl);

        var setParsedTheme = function (theme) {
          if (typeof theme === 'string') {
            return ctrl.$setTheme(theme);
          }

          $q.when(angular.isFunction(theme) ?  theme() : theme)
            .then(function(name) {
              ctrl.$setTheme(name);
            });
        };

        setParsedTheme(getTheme());

        var unwatch = scope.$watch(getTheme, function(theme) {
          if (theme) {
            setParsedTheme(theme);

            if (!ctrl.$shouldWatch) {
              unwatch();
            }
          }
        });
      }
    }
  };
}

/**
 * Special directive that will disable ALL runtime Theme style generation and DOM injection
 *
 * <link rel="stylesheet" href="angular-material.min.css">
 * <link rel="stylesheet" href="angular-material.themes.css">
 *
 * <body md-themes-disabled>
 *  ...
 * </body>
 *
 * Note: Using md-themes-css directive requires the developer to load external
 * theme stylesheets; e.g. custom themes from Material-Tools:
 *
 *       `angular-material.themes.css`
 *
 * Another option is to use the ThemingProvider to configure and disable the attribute
 * conversions; this would obviate the use of the `md-themes-css` directive
 *
 */
function disableThemesDirective() {
  themeConfig.disableTheming = true;

  // Return a 1x-only, first-match attribute directive
  return {
    restrict : 'A',
    priority : '900'
  };
}

function ThemableDirective($mdTheming) {
  return $mdTheming;
}

function parseRules(theme, colorType, rules) {
  checkValidPalette(theme, colorType);

  rules = rules.replace(/THEME_NAME/g, theme.name);
  var themeNameRegex = new RegExp('\\.md-' + theme.name + '-theme', 'g');
  var simpleVariableRegex = /'?"?\{\{\s*([a-zA-Z]+)-(A?\d+|hue-[0-3]|shadow|default)-?(\d\.?\d*)?(contrast)?\s*\}\}'?"?/g;

  // find and replace simple variables where we use a specific hue, not an entire palette
  // eg. "{{primary-100}}"
  // \(' + THEME_COLOR_TYPES.join('\|') + '\)'
  rules = rules.replace(simpleVariableRegex, function(match, colorType, hue, opacity, contrast) {
    if (colorType === 'foreground') {
      if (hue == 'shadow') {
        return theme.foregroundShadow;
      } else {
        return theme.foregroundPalette[hue] || theme.foregroundPalette['1'];
      }
    }

    // `default` is also accepted as a hue-value, because the background palettes are
    // using it as a name for the default hue.
    if (hue.indexOf('hue') === 0 || hue === 'default') {
      hue = theme.colors[colorType].hues[hue];
    }

    return rgba((PALETTES[ theme.colors[colorType].name ][hue] || '')[contrast ? 'contrast' : 'value'], opacity);
  });

  // Matches '{{ primary-color }}', etc
  var hueRegex = new RegExp('(\'|")?{{\\s*([a-zA-Z]+)-(color|contrast)-?(\\d\\.?\\d*)?\\s*}}("|\')?','g');
  var generatedRules = [];

  // For each type, generate rules for each hue (ie. default, md-hue-1, md-hue-2, md-hue-3)
  angular.forEach(['default', 'hue-1', 'hue-2', 'hue-3'], function(hueName) {
    var newRule = rules
      .replace(hueRegex, function(match, _, matchedColorType, hueType, opacity) {
        var color = theme.colors[matchedColorType];
        var palette = PALETTES[color.name];
        var hueValue = color.hues[hueName];
        return rgba(palette[hueValue][hueType === 'color' ? 'value' : 'contrast'], opacity);
      });
    if (hueName !== 'default') {
      newRule = newRule.replace(themeNameRegex, '.md-' + theme.name + '-theme.md-' + hueName);
    }

    // Don't apply a selector rule to the default theme, making it easier to override
    // styles of the base-component
    if (theme.name == 'default') {
      var themeRuleRegex = /((?:\s|>|\.|\w|-|:|\(|\)|\[|\]|"|'|=)*)\.md-default-theme((?:\s|>|\.|\w|-|:|\(|\)|\[|\]|"|'|=)*)/g;

      newRule = newRule.replace(themeRuleRegex, function(match, start, end) {
        return match + ', ' + start + end;
      });
    }
    generatedRules.push(newRule);
  });

  return generatedRules;
}

var rulesByType = {};

// Generate our themes at run time given the state of THEMES and PALETTES
function generateAllThemes($injector, $mdTheming) {
  var head = document.head;
  var firstChild = head ? head.firstElementChild : null;
  var themeCss = !themeConfig.disableTheming && $injector.has('$MD_THEME_CSS') ? $injector.get('$MD_THEME_CSS') : '';

  // Append our custom registered styles to the theme stylesheet.
  themeCss += themeConfig.registeredStyles.join('');

  if (!firstChild) return;
  if (themeCss.length === 0) return; // no rules, so no point in running this expensive task

  // Expose contrast colors for palettes to ensure that text is always readable
  angular.forEach(PALETTES, sanitizePalette);

  // MD_THEME_CSS is a string generated by the build process that includes all the themable
  // components as templates

  // Break the CSS into individual rules
  var rules = themeCss
                  .split(/\}(?!(\}|'|"|;))/)
                  .filter(function(rule) { return rule && rule.trim().length; })
                  .map(function(rule) { return rule.trim() + '}'; });

  THEME_COLOR_TYPES.forEach(function(type) {
    rulesByType[type] = '';
  });

  // Sort the rules based on type, allowing us to do color substitution on a per-type basis
  rules.forEach(function(rule) {
    // First: test that if the rule has '.md-accent', it goes into the accent set of rules
    for (var i = 0, type; type = THEME_COLOR_TYPES[i]; i++) {
      if (rule.indexOf('.md-' + type) > -1) {
        return rulesByType[type] += rule;
      }
    }

    // If no eg 'md-accent' class is found, try to just find 'accent' in the rule and guess from
    // there
    for (i = 0; type = THEME_COLOR_TYPES[i]; i++) {
      if (rule.indexOf(type) > -1) {
        return rulesByType[type] += rule;
      }
    }

    // Default to the primary array
    return rulesByType[DEFAULT_COLOR_TYPE] += rule;
  });

  // If themes are being generated on-demand, quit here. The user will later manually
  // call generateTheme to do this on a theme-by-theme basis.
  if (themeConfig.generateOnDemand) return;

  angular.forEach($mdTheming.THEMES, function(theme) {
    if (!GENERATED[theme.name] && !($mdTheming.defaultTheme() !== 'default' && theme.name === 'default')) {
      generateTheme(theme, theme.name, themeConfig.nonce);
    }
  });


  // *************************
  // Internal functions
  // *************************

  // The user specifies a 'default' contrast color as either light or dark,
  // then explicitly lists which hues are the opposite contrast (eg. A100 has dark, A200 has light)
  function sanitizePalette(palette, name) {
    var defaultContrast = palette.contrastDefaultColor;
    var lightColors = palette.contrastLightColors || [];
    var strongLightColors = palette.contrastStrongLightColors || [];
    var darkColors = palette.contrastDarkColors || [];

    // These colors are provided as space-separated lists
    if (typeof lightColors === 'string') lightColors = lightColors.split(' ');
    if (typeof strongLightColors === 'string') strongLightColors = strongLightColors.split(' ');
    if (typeof darkColors === 'string') darkColors = darkColors.split(' ');

    // Cleanup after ourselves
    delete palette.contrastDefaultColor;
    delete palette.contrastLightColors;
    delete palette.contrastStrongLightColors;
    delete palette.contrastDarkColors;

    // Change { 'A100': '#fffeee' } to { 'A100': { value: '#fffeee', contrast:DARK_CONTRAST_COLOR }
    angular.forEach(palette, function(hueValue, hueName) {
      if (angular.isObject(hueValue)) return; // Already converted
      // Map everything to rgb colors
      var rgbValue = colorToRgbaArray(hueValue);
      if (!rgbValue) {
        throw new Error("Color %1, in palette %2's hue %3, is invalid. Hex or rgb(a) color expected."
                        .replace('%1', hueValue)
                        .replace('%2', palette.name)
                        .replace('%3', hueName));
      }

      palette[hueName] = {
        hex: palette[hueName],
        value: rgbValue,
        contrast: getContrastColor()
      };
      function getContrastColor() {
        if (defaultContrast === 'light') {
          if (darkColors.indexOf(hueName) > -1) {
            return DARK_CONTRAST_COLOR;
          } else {
            return strongLightColors.indexOf(hueName) > -1 ? STRONG_LIGHT_CONTRAST_COLOR
              : LIGHT_CONTRAST_COLOR;
          }
        } else {
          if (lightColors.indexOf(hueName) > -1) {
            return strongLightColors.indexOf(hueName) > -1 ? STRONG_LIGHT_CONTRAST_COLOR
              : LIGHT_CONTRAST_COLOR;
          } else {
            return DARK_CONTRAST_COLOR;
          }
        }
      }
    });
  }
}

function generateTheme(theme, name, nonce) {
  var head = document.head;
  var firstChild = head ? head.firstElementChild : null;

  if (!GENERATED[name]) {
    // For each theme, use the color palettes specified for
    // `primary`, `warn` and `accent` to generate CSS rules.
    THEME_COLOR_TYPES.forEach(function(colorType) {
      var styleStrings = parseRules(theme, colorType, rulesByType[colorType]);
      while (styleStrings.length) {
        var styleContent = styleStrings.shift();
        if (styleContent) {
          var style = document.createElement('style');
          style.setAttribute('md-theme-style', '');
          if (nonce) {
            style.setAttribute('nonce', nonce);
          }
          style.appendChild(document.createTextNode(styleContent));
          head.insertBefore(style, firstChild);
        }
      }
    });

    GENERATED[theme.name] = true;
  }

}


function checkValidPalette(theme, colorType) {
  // If theme attempts to use a palette that doesnt exist, throw error
  if (!PALETTES[ (theme.colors[colorType] || {}).name ]) {
    throw new Error(
      "You supplied an invalid color palette for theme %1's %2 palette. Available palettes: %3"
                    .replace('%1', theme.name)
                    .replace('%2', colorType)
                    .replace('%3', Object.keys(PALETTES).join(', '))
    );
  }
}

function colorToRgbaArray(clr) {
  if (angular.isArray(clr) && clr.length == 3) return clr;
  if (/^rgb/.test(clr)) {
    return clr.replace(/(^\s*rgba?\(|\)\s*$)/g, '').split(',').map(function(value, i) {
      return i == 3 ? parseFloat(value, 10) : parseInt(value, 10);
    });
  }
  if (clr.charAt(0) == '#') clr = clr.substring(1);
  if (!/^([a-fA-F0-9]{3}){1,2}$/g.test(clr)) return;

  var dig = clr.length / 3;
  var red = clr.substr(0, dig);
  var grn = clr.substr(dig, dig);
  var blu = clr.substr(dig * 2);
  if (dig === 1) {
    red += red;
    grn += grn;
    blu += blu;
  }
  return [parseInt(red, 16), parseInt(grn, 16), parseInt(blu, 16)];
}

function rgba(rgbArray, opacity) {
  if (!rgbArray) return "rgb('0,0,0')";

  if (rgbArray.length == 4) {
    rgbArray = angular.copy(rgbArray);
    opacity ? rgbArray.pop() : opacity = rgbArray.pop();
  }
  return opacity && (typeof opacity == 'number' || (typeof opacity == 'string' && opacity.length)) ?
    'rgba(' + rgbArray.join(',') + ',' + opacity + ')' :
    'rgb(' + rgbArray.join(',') + ')';
}


})(window.angular);

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.autocomplete
 */
/*
 * @see js folder for autocomplete implementation
 */
angular.module('material.components.autocomplete', [
  'material.core',
  'material.components.icon',
  'material.components.virtualRepeat'
]);

})();
(function(){
"use strict";


MdAutocompleteCtrl.$inject = ["$scope", "$element", "$mdUtil", "$mdConstant", "$mdTheming", "$window", "$animate", "$rootElement", "$attrs", "$q", "$log", "$mdLiveAnnouncer"];angular
    .module('material.components.autocomplete')
    .controller('MdAutocompleteCtrl', MdAutocompleteCtrl);

var ITEM_HEIGHT   = 48,
    MAX_ITEMS     = 5,
    MENU_PADDING  = 8,
    INPUT_PADDING = 2, // Padding provided by `md-input-container`
    MODE_STANDARD = 'standard',
    MODE_VIRTUAL = 'virtual';

function MdAutocompleteCtrl ($scope, $element, $mdUtil, $mdConstant, $mdTheming, $window,
                             $animate, $rootElement, $attrs, $q, $log, $mdLiveAnnouncer) {

  // Internal Variables.
  var ctrl                 = this,
      itemParts            = $scope.itemsExpr.split(/ in /i),
      itemExpr             = itemParts[ 1 ],
      elements             = null,
      cache                = {},
      noBlur               = false,
      selectedItemWatchers = [],
      hasFocus             = false,
      fetchesInProgress    = 0,
      enableWrapScroll     = null,
      inputModelCtrl       = null,
      debouncedOnResize    = $mdUtil.debounce(onWindowResize),
      mode                 = MODE_VIRTUAL; // default

  /**
   * The root document element. This is used for attaching a top-level click handler to
   * close the options panel when a click outside said panel occurs. We use `documentElement`
   * instead of body because, when scrolling is disabled, some browsers consider the body element
   * to be completely off the screen and propagate events directly to the html element.
   * @type {!Object} angular.JQLite
   */
  ctrl.documentElement = angular.element(document.documentElement);

  // Public Exported Variables with handlers
  defineProperty('hidden', handleHiddenChange, true);

  // Public Exported Variables
  ctrl.scope = $scope;
  ctrl.parent = $scope.$parent;
  ctrl.itemName = itemParts[0];
  ctrl.matches = [];
  ctrl.loading = false;
  ctrl.hidden = true;
  ctrl.index = -1;
  ctrl.activeOption = null;
  ctrl.id = $mdUtil.nextUid();
  ctrl.isDisabled = null;
  ctrl.isRequired = null;
  ctrl.isReadonly = null;
  ctrl.hasNotFound = false;
  ctrl.selectedMessage = $scope.selectedMessage || 'selected';

  // Public Exported Methods
  ctrl.keydown = keydown;
  ctrl.blur = blur;
  ctrl.focus = focus;
  ctrl.clear = clearValue;
  ctrl.select = select;
  ctrl.listEnter = onListEnter;
  ctrl.listLeave = onListLeave;
  ctrl.focusInput = focusInputElement;
  ctrl.getCurrentDisplayValue = getCurrentDisplayValue;
  ctrl.registerSelectedItemWatcher = registerSelectedItemWatcher;
  ctrl.unregisterSelectedItemWatcher = unregisterSelectedItemWatcher;
  ctrl.notFoundVisible = notFoundVisible;
  ctrl.loadingIsVisible = loadingIsVisible;
  ctrl.positionDropdown = positionDropdown;

  /**
   * Report types to be used for the $mdLiveAnnouncer
   * @enum {number} Unique flag id.
   */
  var ReportType = {
    Count: 1,
    Selected: 2
  };

  return init();

  // initialization methods

  /**
   * Initialize the controller, setup watchers, gather elements
   */
  function init () {

    $mdUtil.initOptionalProperties($scope, $attrs, {
      searchText: '',
      selectedItem: null,
      clearButton: false,
      disableVirtualRepeat: false,
    });

    $mdTheming($element);
    configureWatchers();
    $mdUtil.nextTick(function () {

      gatherElements();
      moveDropdown();

      // Touch devices often do not send a click event on tap. We still want to focus the input
      // and open the options pop-up in these cases.
      $element.on('touchstart', focusInputElement);

      // Forward all focus events to the input element when autofocus is enabled
      if ($scope.autofocus) {
        $element.on('focus', focusInputElement);
      }
      if ($scope.inputAriaDescribedBy) {
        elements.input.setAttribute('aria-describedby', $scope.inputAriaDescribedBy);
      }
      if (!$scope.floatingLabel) {
        if ($scope.inputAriaLabel) {
          elements.input.setAttribute('aria-label', $scope.inputAriaLabel);
        } else if ($scope.inputAriaLabelledBy) {
          elements.input.setAttribute('aria-labelledby', $scope.inputAriaLabelledBy);
        } else if ($scope.placeholder) {
          // If no aria-label or aria-labelledby references are defined, then just label using the
          // placeholder.
          elements.input.setAttribute('aria-label', $scope.placeholder);
        }
      }
    });
  }

  function updateModelValidators() {
    if (!$scope.requireMatch || !inputModelCtrl) return;

    inputModelCtrl.$setValidity('md-require-match', !!$scope.selectedItem || !$scope.searchText);
  }

  /**
   * Calculates the dropdown's position and applies the new styles to the menu element
   * @returns {*}
   */
  function positionDropdown () {
    if (!elements) {
      return $mdUtil.nextTick(positionDropdown, false, $scope);
    }

    var dropdownHeight = ($scope.dropdownItems || MAX_ITEMS) * ITEM_HEIGHT;
    var hrect  = elements.wrap.getBoundingClientRect(),
        vrect  = elements.snap.getBoundingClientRect(),
        root   = elements.root.getBoundingClientRect(),
        top    = vrect.bottom - root.top,
        bot    = root.bottom - vrect.top,
        left   = hrect.left - root.left,
        width  = hrect.width,
        offset = getVerticalOffset(),
        position = $scope.dropdownPosition,
        styles, enoughBottomSpace, enoughTopSpace;
    var bottomSpace = root.bottom - vrect.bottom - MENU_PADDING + $mdUtil.getViewportTop();
    var topSpace = vrect.top - MENU_PADDING;

    // Automatically determine dropdown placement based on available space in viewport.
    if (!position) {
      enoughTopSpace = topSpace > dropdownHeight;
      enoughBottomSpace = bottomSpace > dropdownHeight;
      if (enoughBottomSpace) {
        position = 'bottom';
      } else if (enoughTopSpace) {
        position = 'top';
      } else {
        position = topSpace > bottomSpace ? 'top' : 'bottom';
      }
    }
    // Adjust the width to account for the padding provided by `md-input-container`
    if ($attrs.mdFloatingLabel) {
      left += INPUT_PADDING;
      width -= INPUT_PADDING * 2;
    }
    styles = {
      left:     left + 'px',
      minWidth: width + 'px',
      maxWidth: Math.max(hrect.right - root.left, root.right - hrect.left) - MENU_PADDING + 'px'
    };

    if (position === 'top') {
      styles.top       = 'auto';
      styles.bottom    = bot + 'px';
      styles.maxHeight = Math.min(dropdownHeight, topSpace) + 'px';
    } else {
      bottomSpace = root.bottom - hrect.bottom - MENU_PADDING + $mdUtil.getViewportTop();

      styles.top       = (top - offset) + 'px';
      styles.bottom    = 'auto';
      styles.maxHeight = Math.min(dropdownHeight, bottomSpace) + 'px';
    }

    elements.$.scrollContainer.css(styles);
    $mdUtil.nextTick(correctHorizontalAlignment, false, $scope);

    /**
     * Calculates the vertical offset for floating label examples to account for ngMessages
     * @returns {number}
     */
    function getVerticalOffset () {
      var offset = 0;
      var inputContainer = $element.find('md-input-container');
      if (inputContainer.length) {
        var input = inputContainer.find('input');
        offset = inputContainer.prop('offsetHeight');
        offset -= input.prop('offsetTop');
        offset -= input.prop('offsetHeight');
        // add in the height left up top for the floating label text
        offset += inputContainer.prop('offsetTop');
      }
      return offset;
    }

    /**
     * Makes sure that the menu doesn't go off of the screen on either side.
     */
    function correctHorizontalAlignment () {
      var dropdown = elements.scrollContainer.getBoundingClientRect(),
          styles   = {};
      if (dropdown.right > root.right) {
        styles.left = (hrect.right - dropdown.width) + 'px';
      }
      elements.$.scrollContainer.css(styles);
    }
  }

  /**
   * Moves the dropdown menu to the body tag in order to avoid z-index and overflow issues.
   */
  function moveDropdown () {
    if (!elements.$.root.length) return;
    $mdTheming(elements.$.scrollContainer);
    elements.$.scrollContainer.detach();
    elements.$.root.append(elements.$.scrollContainer);
    if ($animate.pin) $animate.pin(elements.$.scrollContainer, $rootElement);
  }

  /**
   * Sends focus to the input element.
   */
  function focusInputElement () {
    elements.input.focus();
  }

  /**
   * Update the activeOption based on the selected item in the listbox.
   * The activeOption is used in the template to set the aria-activedescendant attribute, which
   * enables screen readers to properly handle visual focus within the listbox and announce the
   * item's place in the list. I.e. "List item 3 of 50". Anytime that `ctrl.index` changes, this
   * function needs to be called to update the activeOption.
   */
  function updateActiveOption() {
    var selectedOption = elements.scroller.querySelector('.selected');
    if (selectedOption) {
      ctrl.activeOption = selectedOption.id;
    } else {
      ctrl.activeOption = null;
    }
  }

  /**
   * Sets up any watchers used by autocomplete
   */
  function configureWatchers () {
    var wait = parseInt($scope.delay, 10) || 0;

    $attrs.$observe('disabled', function (value) { ctrl.isDisabled = $mdUtil.parseAttributeBoolean(value, false); });
    $attrs.$observe('required', function (value) { ctrl.isRequired = $mdUtil.parseAttributeBoolean(value, false); });
    $attrs.$observe('readonly', function (value) { ctrl.isReadonly = $mdUtil.parseAttributeBoolean(value, false); });

    $scope.$watch('searchText', wait ? $mdUtil.debounce(handleSearchText, wait) : handleSearchText);
    $scope.$watch('selectedItem', selectedItemChange);

    angular.element($window).on('resize', debouncedOnResize);

    $scope.$on('$destroy', cleanup);
  }

  /**
   * Removes any events or leftover elements created by this controller
   */
  function cleanup () {
    if (!ctrl.hidden) {
      $mdUtil.enableScrolling();
    }

    angular.element($window).off('resize', debouncedOnResize);

    if (elements){
      var items = ['ul', 'scroller', 'scrollContainer', 'input'];
      angular.forEach(items, function(key){
        elements.$[key].remove();
      });
    }
  }

  /**
   * Event handler to be called whenever the window resizes.
   */
  function onWindowResize() {
    if (!ctrl.hidden) {
      positionDropdown();
    }
  }

  /**
   * Gathers all of the elements needed for this controller
   */
  function gatherElements () {

    var snapWrap = gatherSnapWrap();

    elements = {
      main:  $element[0],
      scrollContainer: $element[0].querySelector('.md-virtual-repeat-container, .md-standard-list-container'),
      scroller: $element[0].querySelector('.md-virtual-repeat-scroller, .md-standard-list-scroller'),
      ul:    $element.find('ul')[0],
      input: $element.find('input')[0],
      wrap:  snapWrap.wrap,
      snap:  snapWrap.snap,
      root:  document.body,
    };

    elements.li   = elements.ul.getElementsByTagName('li');
    elements.$    = getAngularElements(elements);
    mode = elements.scrollContainer.classList.contains('md-standard-list-container') ? MODE_STANDARD : MODE_VIRTUAL;
    inputModelCtrl = elements.$.input.controller('ngModel');
  }

  /**
   * Gathers the snap and wrap elements
   *
   */
  function gatherSnapWrap() {
    var element;
    var value;
    for (element = $element; element.length; element = element.parent()) {
      value = element.attr('md-autocomplete-snap');
      if (angular.isDefined(value)) break;
    }

    if (element.length) {
      return {
        snap: element[0],
        wrap: (value.toLowerCase() === 'width') ? element[0] : $element.find('md-autocomplete-wrap')[0]
      };
    }

    var wrap = $element.find('md-autocomplete-wrap')[0];
    return {
      snap: wrap,
      wrap: wrap
    };
  }

  /**
   * Gathers angular-wrapped versions of each element
   * @param elements
   * @returns {{}}
   */
  function getAngularElements (elements) {
    var obj = {};
    for (var key in elements) {
      if (elements.hasOwnProperty(key)) obj[ key ] = angular.element(elements[ key ]);
    }
    return obj;
  }

  // event/change handlers

  /**
   * @param {Event} $event
   */
  function preventDefault($event) {
    $event.preventDefault();
  }

  /**
   * @param {Event} $event
   */
  function stopPropagation($event) {
    $event.stopPropagation();
  }

  /**
   * Handles changes to the `hidden` property.
   * @param {boolean} hidden true to hide the options pop-up, false to show it.
   * @param {boolean} oldHidden the previous value of hidden
   */
  function handleHiddenChange (hidden, oldHidden) {
    var scrollContainerElement;

    if (elements) {
      scrollContainerElement = angular.element(elements.scrollContainer);
    }
    if (!hidden && oldHidden) {
      positionDropdown();

      // Report in polite mode, because the screen reader should finish the default description of
      // the input element.
      reportMessages(true, ReportType.Count | ReportType.Selected);

      if (elements) {
        $mdUtil.disableScrollAround(elements.scrollContainer);
        enableWrapScroll = disableElementScrollEvents(elements.wrap);
        if ($mdUtil.isIos) {
          ctrl.documentElement.on('touchend', handleTouchOutsidePanel);
          if (scrollContainerElement) {
            scrollContainerElement.on('touchstart touchmove touchend', stopPropagation);
          }
        }
        ctrl.index = getDefaultIndex();
        $mdUtil.nextTick(function() {
          updateActiveOption();
          updateScroll();
        });
      }
    } else if (hidden && !oldHidden) {
      if ($mdUtil.isIos) {
        ctrl.documentElement.off('touchend', handleTouchOutsidePanel);
        if (scrollContainerElement) {
          scrollContainerElement.off('touchstart touchmove touchend', stopPropagation);
        }
      }
      $mdUtil.enableScrolling();

      if (enableWrapScroll) {
        enableWrapScroll();
        enableWrapScroll = null;
      }
    }
  }

  /**
   * Handling touch events that bubble up to the document is required for closing the dropdown
   * panel on touch outside of the options pop-up panel on iOS.
   * @param {Event} $event
   */
  function handleTouchOutsidePanel($event) {
    ctrl.hidden = true;
    // iOS does not blur the pop-up for touches on the scroll mask, so we have to do it.
    doBlur(true);
  }

  /**
   * Disables scrolling for a specific element.
   * @param {!string|!DOMElement} element to disable scrolling
   * @return {Function} function to call to re-enable scrolling for the element
   */
  function disableElementScrollEvents(element) {
    var elementToDisable = angular.element(element);
    elementToDisable.on('wheel touchmove', preventDefault);

    return function() {
      elementToDisable.off('wheel touchmove', preventDefault);
    };
  }

  /**
   * When the user mouses over the dropdown menu, ignore blur events.
   */
  function onListEnter () {
    noBlur = true;
  }

  /**
   * When the user's mouse leaves the menu, blur events may hide the menu again.
   */
  function onListLeave () {
    if (!hasFocus && !ctrl.hidden) elements.input.focus();
    noBlur = false;
    ctrl.hidden = shouldHide();
  }

  /**
   * Handles changes to the selected item.
   * @param selectedItem
   * @param previousSelectedItem
   */
  function selectedItemChange (selectedItem, previousSelectedItem) {

    updateModelValidators();

    if (selectedItem) {
      getDisplayValue(selectedItem).then(function (val) {
        $scope.searchText = val;
        handleSelectedItemChange(selectedItem, previousSelectedItem);
      });
    } else if (previousSelectedItem && $scope.searchText) {
      getDisplayValue(previousSelectedItem).then(function(displayValue) {
        // Clear the searchText, when the selectedItem is set to null.
        // Do not clear the searchText, when the searchText isn't matching with the previous
        // selected item.
        if (angular.isString($scope.searchText)
          && displayValue.toString().toLowerCase() === $scope.searchText.toLowerCase()) {
          $scope.searchText = '';
        }
      });
    }

    if (selectedItem !== previousSelectedItem) {
      announceItemChange();
    }
  }

  /**
   * Use the user-defined expression to announce changes each time a new item is selected
   */
  function announceItemChange () {
    angular.isFunction($scope.itemChange) &&
      $scope.itemChange(getItemAsNameVal($scope.selectedItem));
  }

  /**
   * Use the user-defined expression to announce changes each time the search text is changed
   */
  function announceTextChange () {
    angular.isFunction($scope.textChange) && $scope.textChange();
  }

  /**
   * Calls any external watchers listening for the selected item.  Used in conjunction with
   * `registerSelectedItemWatcher`.
   * @param selectedItem
   * @param previousSelectedItem
   */
  function handleSelectedItemChange (selectedItem, previousSelectedItem) {
    selectedItemWatchers.forEach(function (watcher) {
      watcher(selectedItem, previousSelectedItem);
    });
  }

  /**
   * Register a function to be called when the selected item changes.
   * @param cb
   */
  function registerSelectedItemWatcher (cb) {
    if (selectedItemWatchers.indexOf(cb) === -1) {
      selectedItemWatchers.push(cb);
    }
  }

  /**
   * Unregister a function previously registered for selected item changes.
   * @param cb
   */
  function unregisterSelectedItemWatcher (cb) {
    var i = selectedItemWatchers.indexOf(cb);
    if (i !== -1) {
      selectedItemWatchers.splice(i, 1);
    }
  }

  /**
   * Handles changes to the searchText property.
   * @param {string} searchText
   * @param {string} previousSearchText
   */
  function handleSearchText (searchText, previousSearchText) {
    ctrl.index = getDefaultIndex();

    // do nothing on init
    if (searchText === previousSearchText) return;

    updateModelValidators();

    getDisplayValue($scope.selectedItem).then(function (val) {
      // clear selected item if search text no longer matches it
      if (searchText !== val) {
        $scope.selectedItem = null;

        // trigger change event if available
        if (searchText !== previousSearchText) {
          announceTextChange();
        }

        // cancel results if search text is not long enough
        if (!isMinLengthMet()) {
          ctrl.matches = [];

          setLoading(false);
          reportMessages(true, ReportType.Count);

        } else {
          handleQuery();
        }
      }
    });

  }

  /**
   * Handles input blur event, determines if the dropdown should hide.
   */
  function blur($event) {
    hasFocus = false;

    if (!noBlur) {
      ctrl.hidden = shouldHide();
      evalAttr('ngBlur', { $event: $event });
    }
  }

  /**
   * Force blur on input element
   * @param {boolean} forceBlur
   */
  function doBlur(forceBlur) {
    if (forceBlur) {
      noBlur = false;
      hasFocus = false;
    }
    elements.input.blur();
  }

  /**
   * Handles input focus event, determines if the dropdown should show.
   */
  function focus($event) {
    hasFocus = true;

    if (isSearchable() && isMinLengthMet()) {
      handleQuery();
    }

    ctrl.hidden = shouldHide();

    evalAttr('ngFocus', { $event: $event });
  }

  /**
   * Handles keyboard input.
   * @param event
   */
  function keydown (event) {
    switch (event.keyCode) {
      case $mdConstant.KEY_CODE.DOWN_ARROW:
        if (ctrl.loading || hasSelection()) return;
        event.stopPropagation();
        event.preventDefault();
        ctrl.index = ctrl.index + 1 > ctrl.matches.length - 1 ? 0 : Math.min(ctrl.index + 1, ctrl.matches.length - 1);
        $mdUtil.nextTick(updateActiveOption);
        updateScroll();
        break;
      case $mdConstant.KEY_CODE.UP_ARROW:
        if (ctrl.loading || hasSelection()) return;
        event.stopPropagation();
        event.preventDefault();
        ctrl.index = ctrl.index - 1 < 0 ? ctrl.matches.length - 1 : Math.max(0, ctrl.index - 1);
        $mdUtil.nextTick(updateActiveOption);
        updateScroll();
        break;
      case $mdConstant.KEY_CODE.TAB:
        // If we hit tab, assume that we've left the list so it will close
        onListLeave();

        if (ctrl.hidden || ctrl.loading || ctrl.index < 0 || ctrl.matches.length < 1) return;
        select(ctrl.index);
        break;
      case $mdConstant.KEY_CODE.ENTER:
        if (ctrl.hidden || ctrl.loading || ctrl.index < 0 || ctrl.matches.length < 1) return;
        if (hasSelection()) return;
        event.stopImmediatePropagation();
        event.preventDefault();
        select(ctrl.index);
        break;
      case $mdConstant.KEY_CODE.ESCAPE:
        event.preventDefault(); // Prevent browser from always clearing input
        if (!shouldProcessEscape()) return;
        event.stopPropagation();

        clearSelectedItem();
        if ($scope.searchText && hasEscapeOption('clear')) {
          clearSearchText();
        }

        // Manually hide (needed for mdNotFound support)
        ctrl.hidden = true;

        if (hasEscapeOption('blur')) {
          // Force the component to blur if they hit escape
          doBlur(true);
        }

        break;
      default:
    }
  }

  // getters

  /**
   * Returns the minimum length needed to display the dropdown.
   * @returns {*}
   */
  function getMinLength () {
    return angular.isNumber($scope.minLength) ? $scope.minLength : 1;
  }

  /**
   * Returns the display value for an item.
   * @param {*} item
   * @returns {*}
   */
  function getDisplayValue (item) {
    return $q.when(getItemText(item) || item).then(function(itemText) {
      if (itemText && !angular.isString(itemText)) {
        $log.warn('md-autocomplete: Could not resolve display value to a string. ' +
          'Please check the `md-item-text` attribute.');
      }

      return itemText;
    });

    /**
     * Getter function to invoke user-defined expression (in the directive)
     * to convert your object to a single string.
     * @param {*} item
     * @returns {string|null}
     */
    function getItemText (item) {
      return (item && $scope.itemText) ? $scope.itemText(getItemAsNameVal(item)) : null;
    }
  }

  /**
   * Returns the locals object for compiling item templates.
   * @param {*} item
   * @returns {Object|undefined}
   */
  function getItemAsNameVal (item) {
    if (!item) {
      return undefined;
    }

    var locals = {};
    if (ctrl.itemName) {
      locals[ ctrl.itemName ] = item;
    }

    return locals;
  }

  /**
   * Returns the default index based on whether or not autoselect is enabled.
   * @returns {number} 0 if autoselect is enabled, -1 if not.
   */
  function getDefaultIndex () {
    return $scope.autoselect ? 0 : -1;
  }

  /**
   * Sets the loading parameter and updates the hidden state.
   * @param value {boolean} Whether or not the component is currently loading.
   */
  function setLoading(value) {
    if (ctrl.loading !== value) {
      ctrl.loading = value;
    }

    // Always refresh the hidden variable as something else might have changed
    ctrl.hidden = shouldHide();
  }

  /**
   * Determines if the menu should be hidden.
   * @returns {boolean} true if the menu should be hidden
   */
  function shouldHide () {
    return !shouldShow();
  }

  /**
   * Determines whether the autocomplete is able to query within the current state.
   * @returns {boolean} true if the query can be run
   */
  function isSearchable() {
    if (ctrl.loading && !hasMatches()) {
      // No query when query is in progress.
      return false;
    } else if (hasSelection()) {
      // No query if there is already a selection
      return false;
    }
    else if (!hasFocus) {
      // No query if the input does not have focus
      return false;
    }
    return true;
  }

  /**
   * @returns {boolean} if the escape keydown should be processed, return true.
   *  Otherwise return false.
   */
  function shouldProcessEscape() {
    return hasEscapeOption('blur') || !ctrl.hidden || ctrl.loading || hasEscapeOption('clear') && $scope.searchText;
  }

  /**
   * @param {string} option check if this option is set
   * @returns {boolean} if the specified escape option is set, return true. Return false otherwise.
   */
  function hasEscapeOption(option) {
    return !$scope.escapeOptions || $scope.escapeOptions.toLowerCase().indexOf(option) !== -1;
  }

  /**
   * Determines if the menu should be shown.
   * @returns {boolean} true if the menu should be shown
   */
  function shouldShow() {
    if (ctrl.isReadonly) {
      // Don't show if read only is set
      return false;
    } else if (!isSearchable()) {
      // Don't show if a query is in progress, there is already a selection,
      // or the input is not focused.
      return false;
    }
    return (isMinLengthMet() && hasMatches()) || notFoundVisible();
  }

  /**
   * @returns {boolean} true if the search text has matches.
   */
  function hasMatches() {
    return ctrl.matches.length ? true : false;
  }

  /**
   * @returns {boolean} true if the autocomplete has a valid selection.
   */
  function hasSelection() {
    return ctrl.scope.selectedItem ? true : false;
  }

  /**
   * @returns {boolean} true if the loading indicator is, or should be, visible.
   */
  function loadingIsVisible() {
    return ctrl.loading && !hasSelection();
  }

  /**
   * @returns {*} the display value of the current item.
   */
  function getCurrentDisplayValue () {
    return getDisplayValue(ctrl.matches[ ctrl.index ]);
  }

  /**
   * Determines if the minimum length is met by the search text.
   * @returns {*} true if the minimum length is met by the search text
   */
  function isMinLengthMet () {
    return ($scope.searchText || '').length >= getMinLength();
  }

  // actions

  /**
   * Defines a public property with a handler and a default value.
   * @param {string} key
   * @param {Function} handler function
   * @param {*} defaultValue default value
   */
  function defineProperty (key, handler, defaultValue) {
    Object.defineProperty(ctrl, key, {
      get: function () { return defaultValue; },
      set: function (newValue) {
        var oldValue = defaultValue;
        defaultValue        = newValue;
        handler(newValue, oldValue);
      }
    });
  }

  /**
   * Selects the item at the given index.
   * @param {number} index to select
   */
  function select (index) {
    // force form to update state for validation
    $mdUtil.nextTick(function () {
      getDisplayValue(ctrl.matches[ index ]).then(function (val) {
        var ngModel = elements.$.input.controller('ngModel');
        $mdLiveAnnouncer.announce(val + ' ' + ctrl.selectedMessage, 'assertive');
        ngModel.$setViewValue(val);
        ngModel.$render();
      }).finally(function () {
        $scope.selectedItem = ctrl.matches[ index ];
        setLoading(false);
      });
    }, false);
  }

  /**
   * Clears the searchText value and selected item.
   * @param {Event} $event
   */
  function clearValue ($event) {
    if ($event) {
      $event.stopPropagation();
    }
    clearSelectedItem();
    clearSearchText();
  }

  /**
   * Clears the selected item
   */
  function clearSelectedItem () {
    // Reset our variables
    ctrl.index = -1;
    $mdUtil.nextTick(updateActiveOption);
    ctrl.matches = [];
  }

  /**
   * Clears the searchText value
   */
  function clearSearchText () {
    // Set the loading to true so we don't see flashes of content.
    // The flashing will only occur when an async request is running.
    // So the loading process will stop when the results had been retrieved.
    setLoading(true);

    $scope.searchText = '';

    // Normally, triggering the change / input event is unnecessary, because the browser detects it properly.
    // But some browsers are not detecting it properly, which means that we have to trigger the event.
    // Using the `input` is not working properly, because for example IE11 is not supporting the `input` event.
    // The `change` event is a good alternative and is supported by all supported browsers.
    var eventObj = document.createEvent('CustomEvent');
    eventObj.initCustomEvent('change', true, true, { value: '' });
    elements.input.dispatchEvent(eventObj);

    // For some reason, firing the above event resets the value of $scope.searchText if
    // $scope.searchText has a space character at the end, so we blank it one more time and then
    // focus.
    elements.input.blur();
    $scope.searchText = '';
    elements.input.focus();
  }

  /**
   * Fetches the results for the provided search text.
   * @param searchText
   */
  function fetchResults (searchText) {
    var items = $scope.$parent.$eval(itemExpr),
        term  = searchText.toLowerCase(),
        isList = angular.isArray(items),
        isPromise = !!items.then; // Every promise should contain a `then` property

    if (isList) onResultsRetrieved(items);
    else if (isPromise) handleAsyncResults(items);

    function handleAsyncResults(items) {
      if (!items) return;

      items = $q.when(items);
      fetchesInProgress++;
      setLoading(true);

      $mdUtil.nextTick(function () {
          items
            .then(onResultsRetrieved)
            .finally(function(){
              if (--fetchesInProgress === 0) {
                setLoading(false);
              }
            });
      },true, $scope);
    }

    function onResultsRetrieved(matches) {
      cache[term] = matches;

      // Just cache the results if the request is now outdated.
      // The request becomes outdated, when the new searchText has changed during the result fetching.
      if ((searchText || '') !== ($scope.searchText || '')) {
        return;
      }

      handleResults(matches);
    }
  }


  /**
   * Reports given message types to supported screen readers.
   * @param {boolean} isPolite Whether the announcement should be polite.
   * @param {!number} types Message flags to be reported to the screen reader.
   */
  function reportMessages(isPolite, types) {
    var politeness = isPolite ? 'polite' : 'assertive';
    var messages = [];

    if (types & ReportType.Selected && ctrl.index !== -1) {
      messages.push(getCurrentDisplayValue());
    }

    if (types & ReportType.Count) {
      messages.push($q.resolve(getCountMessage()));
    }

    $q.all(messages).then(function(data) {
      $mdLiveAnnouncer.announce(data.join(' '), politeness);
    });
  }

  /**
   * @returns {string} the ARIA message for how many results match the current query.
   */
  function getCountMessage () {
    switch (ctrl.matches.length) {
      case 0:
        return 'There are no matches available.';
      case 1:
        return 'There is 1 match available.';
      default:
        return 'There are ' + ctrl.matches.length + ' matches available.';
    }
  }

  /**
   * Makes sure that the focused element is within view.
   */
  function updateScroll () {
    if (!elements.li[0]) return;
    if (mode === MODE_STANDARD) {
      updateStandardScroll();
    } else {
      updateVirtualScroll();
    }
  }

  function updateVirtualScroll() {
    // elements in virtual scroll have consistent heights
    var optionHeight = elements.li[0].offsetHeight,
        top = optionHeight * Math.max(0, ctrl.index),
        bottom = top + optionHeight,
        containerHeight = elements.scroller.clientHeight,
        scrollTop = elements.scroller.scrollTop;

    if (top < scrollTop) {
      scrollTo(top);
    } else if (bottom > scrollTop + containerHeight) {
      scrollTo(bottom - containerHeight);
    }
  }

  function updateStandardScroll() {
    // elements in standard scroll have variable heights
    var selected =  elements.li[Math.max(0, ctrl.index)];
    var containerHeight = elements.scrollContainer.offsetHeight,
        top = selected && selected.offsetTop || 0,
        bottom = top + selected.clientHeight,
        scrollTop = elements.scrollContainer.scrollTop;

    if (top < scrollTop) {
      scrollTo(top);
    } else if (bottom > scrollTop + containerHeight) {
      scrollTo(bottom - containerHeight);
    }
  }

  function isPromiseFetching() {
    return fetchesInProgress !== 0;
  }

  function scrollTo (offset) {
    if (mode === MODE_STANDARD) {
      elements.scrollContainer.scrollTop = offset;
    } else {
      elements.$.scrollContainer.controller('mdVirtualRepeatContainer').scrollTo(offset);
    }
  }

  function notFoundVisible () {
    var textLength = (ctrl.scope.searchText || '').length;

    return ctrl.hasNotFound && !hasMatches() && (!ctrl.loading || isPromiseFetching()) && textLength >= getMinLength() && (hasFocus || noBlur) && !hasSelection();
  }

  /**
   * Starts the query to gather the results for the current searchText.  Attempts to return cached
   * results first, then forwards the process to `fetchResults` if necessary.
   */
  function handleQuery () {
    var searchText = $scope.searchText || '';
    var term = searchText.toLowerCase();

    // If caching is enabled and the current searchText is stored in the cache
    if (!$scope.noCache && cache[term]) {
      // The results should be handled as same as a normal un-cached request does.
      handleResults(cache[term]);
    } else {
      fetchResults(searchText);
    }

    ctrl.hidden = shouldHide();
  }

  /**
   * Handles the retrieved results by showing them in the autocompletes dropdown.
   * @param results Retrieved results
   */
  function handleResults(results) {
    ctrl.matches = results;
    ctrl.hidden  = shouldHide();

    // If loading is in progress, then we'll end the progress. This is needed for example,
    // when the `clear` button was clicked, because there we always show the loading process, to prevent flashing.
    if (ctrl.loading) setLoading(false);

    if ($scope.selectOnMatch) selectItemOnMatch();

    positionDropdown();
    reportMessages(true, ReportType.Count);
  }

  /**
   * If there is only one matching item and the search text matches its display value exactly,
   * automatically select that item.  Note: This function is only called if the user uses the
   * `md-select-on-match` flag.
   */
  function selectItemOnMatch () {
    var searchText = $scope.searchText,
        matches    = ctrl.matches,
        item       = matches[ 0 ];
    if (matches.length === 1) getDisplayValue(item).then(function (displayValue) {
      var isMatching = searchText === displayValue;
      if ($scope.matchInsensitive && !isMatching) {
        isMatching = searchText.toLowerCase() === displayValue.toLowerCase();
      }

      if (isMatching) {
        select(0);
      }
    });
  }

  /**
   * Evaluates an attribute expression against the parent scope.
   * @param {String} attr Name of the attribute to be evaluated.
   * @param {Object?} locals Properties to be injected into the evaluation context.
   */
 function evalAttr(attr, locals) {
    if ($attrs[attr]) {
      $scope.$parent.$eval($attrs[attr], locals || {});
    }
  }

}

})();
(function(){
"use strict";


MdAutocomplete.$inject = ["$$mdSvgRegistry"];angular
    .module('material.components.autocomplete')
    .directive('mdAutocomplete', MdAutocomplete);

/**
 * @ngdoc directive
 * @name mdAutocomplete
 * @module material.components.autocomplete
 *
 * @description
 * `<md-autocomplete>` is a special input component with a drop-down of all possible matches to a
 *     custom query. This component allows you to provide real-time suggestions as the user types
 *     in the input area.
 *
 * To start, you will need to specify the required parameters and provide a template for your
 *     results. The content inside `md-autocomplete` will be treated as a template.
 *
 * In more complex cases, you may want to include other content such as a message to display when
 *     no matches were found.  You can do this by wrapping your template in `md-item-template` and
 *     adding a tag for `md-not-found`.  An example of this is shown below.
 *
 * To reset the displayed value you must clear both values for `md-search-text` and
 * `md-selected-item`.
 *
 * ### Validation
 *
 * You can use `ng-messages` to include validation the same way that you would normally validate;
 *     however, if you want to replicate a standard input with a floating label, you will have to
 *     do the following:
 *
 * - Make sure that your template is wrapped in `md-item-template`
 * - Add your `ng-messages` code inside of `md-autocomplete`
 * - Add your validation properties to `md-autocomplete` (ie. `required`)
 * - Add a `name` to `md-autocomplete` (to be used on the generated `input`)
 *
 * There is an example below of how this should look.
 *
 * ### Snapping Drop-Down
 *
 * You can cause the autocomplete drop-down to snap to an ancestor element by applying the
 *     `md-autocomplete-snap` attribute to that element. You can also snap to the width of
 *     the `md-autocomplete-snap` element by setting the attribute's value to `width`
 *     (ie. `md-autocomplete-snap="width"`).
 *
 * ### Notes
 *
 * **Autocomplete Dropdown Items Rendering**
 *
 * The `md-autocomplete` uses the the <a ng-href="api/directive/mdVirtualRepeat">
 *   mdVirtualRepeat</a> directive for displaying the results inside of the dropdown.<br/>
 *
 * > When encountering issues regarding the item template please take a look at the
 *   <a ng-href="api/directive/mdVirtualRepeatContainer">VirtualRepeatContainer</a> documentation.
 *
 * **Autocomplete inside of a Virtual Repeat**
 *
 * When using the `md-autocomplete` directive inside of a
 * <a ng-href="api/directive/mdVirtualRepeatContainer">VirtualRepeatContainer</a> the dropdown items
 * might not update properly, because caching of the results is enabled by default.
 *
 * The autocomplete will then show invalid dropdown items, because the Virtual Repeat only updates
 * the scope bindings rather than re-creating the `md-autocomplete`. This means that the previous
 * cached results will be used.
 *
 * > To avoid such problems, ensure that the autocomplete does not cache any results via
 * `md-no-cache="true"`:
 *
 * <hljs lang="html">
 *   <md-autocomplete
 *       md-no-cache="true"
 *       md-selected-item="selectedItem"
 *       md-items="item in items"
 *       md-search-text="searchText"
 *       md-item-text="item.display">
 *     <span>{{ item.display }}</span>
 *   </md-autocomplete>
 * </hljs>
 *
 *
 * @param {expression} md-items An expression in the format of `item in results` to iterate over
 *     matches for your search.<br/><br/>
 *     The `results` expression can be also a function, which returns the results synchronously
 *     or asynchronously (per Promise).
 * @param {expression=} md-selected-item-change An expression to be run each time a new item is
 *     selected.
 * @param {expression=} md-search-text-change An expression to be run each time the search text
 *     updates.
 * @param {expression=} md-search-text A model to bind the search query text to.
 * @param {object=} md-selected-item A model to bind the selected item to.
 * @param {expression=} md-item-text An expression that will convert your object to a single string.
 * @param {string=} placeholder Placeholder text that will be forwarded to the input.
 * @param {boolean=} md-no-cache Disables the internal caching that happens in autocomplete.
 * @param {boolean=} ng-disabled Determines whether or not to disable the input field.
 * @param {boolean=} md-require-match When set to true, the autocomplete will add a validator,
 *     which will evaluate to false, when no item is currently selected.
 * @param {number=} md-min-length Specifies the minimum length of text before autocomplete will
 *     make suggestions.
 * @param {number=} md-delay Specifies the amount of time (in milliseconds) to wait before looking
 *     for results.
 * @param {boolean=} md-clear-button Whether the clear button for the autocomplete input should show
 *     up or not. When `md-floating-label` is set, defaults to false, defaults to true otherwise.
 * @param {boolean=} md-autofocus If true, the autocomplete will be automatically focused when a
 *     `$mdDialog`, `$mdBottomsheet` or `$mdSidenav`, which contains the autocomplete, is opening.
 *     <br/><br/>
 *     Also the autocomplete will immediately focus the input element.
 * @param {boolean=} md-no-asterisk When present, asterisk will not be appended to the floating
 *     label.
 * @param {boolean=} md-autoselect If set to true, the first item will be automatically selected
 *     in the dropdown upon open.
 * @param {string=} md-input-name The name attribute given to the input element to be used with
 *     FormController.
 * @param {string=} md-menu-class This class will be applied to the dropdown menu for styling.
 * @param {string=} md-menu-container-class This class will be applied to the parent container
 *     of the dropdown panel.
 * @param {string=} md-input-class This will be applied to the input for styling. This attribute
 *     is only valid when a `md-floating-label` is defined.
 * @param {string=} md-floating-label This will add a floating label to autocomplete and wrap it in
 *     `md-input-container`.
 * @param {string=} md-select-on-focus When present the input's text will be automatically selected
 *     on focus.
 * @param {string=} md-input-id An ID to be added to the input element.
 * @param {number=} md-input-minlength The minimum length for the input's value for validation.
 * @param {number=} md-input-maxlength The maximum length for the input's value for validation.
 * @param {boolean=} md-select-on-match When set, autocomplete will automatically select
 *     the item if the search text is an exact match. <br/><br/>
 *     An exact match is when only one match is displayed.
 * @param {boolean=} md-match-case-insensitive When set and using `md-select-on-match`, autocomplete
 *     will select on case-insensitive match.
 * @param {string=} md-escape-options Override escape key logic. Default is `blur clear`.<br/>
 *     Options: `blur`, `clear`, `none`.
 * @param {string=} md-dropdown-items Specifies the maximum amount of items to be shown in
 *     the dropdown.<br/><br/>
 *     When the dropdown doesn't fit into the viewport, the dropdown will shrink
 *     as much as possible.
 * @param {string=} md-dropdown-position Overrides the default dropdown position. Options: `top`,
 *    `bottom`.
 * @param {string=} input-aria-describedby A space-separated list of element IDs. This should
 *     contain the IDs of any elements that describe this autocomplete. Screen readers will read the
 *     content of these elements at the end of announcing that the autocomplete has been selected
 *     and describing its current state. The descriptive elements do not need to be visible on the
 *     page.
 * @param {string=} input-aria-labelledby A space-separated list of element IDs. The ideal use case
 *     is that this would contain the ID of a `<label>` element that is associated with this
 *     autocomplete. This will only have affect when `md-floating-label` is not defined.<br><br>
 *     For `<label id="state">US State</label>`, you would set this to
 *     `input-aria-labelledby="state"`.
 * @param {string=} input-aria-label A label that will be applied to the autocomplete's input.
 *    This will be announced by screen readers before the placeholder.
 *    This will only have affect when `md-floating-label` is not defined. If you define both
 *    `input-aria-label` and `input-aria-labelledby`, then `input-aria-label` will take precedence.
 * @param {string=} md-selected-message Attribute to specify the text that the screen reader will
 *    announce after a value is selected. Default is: "selected". If `Alaska` is selected in the
 *    options panel, it will read "Alaska selected". You will want to override this when your app
 *    is running in a non-English locale.
 * @param {boolean=} ng-trim If set to false, the search text will be not trimmed automatically.
 *     Defaults to true.
 * @param {string=} ng-pattern Adds the pattern validator to the ngModel of the search text.
 *     See the [ngPattern Directive](https://docs.angularjs.org/api/ng/directive/ngPattern)
 *     for more details.
 * @param {string=} md-mode Specify the repeat mode for suggestion lists. Acceptable values include
 *     `virtual` (md-virtual-repeat) and `standard` (ng-repeat). See the
 *     `Specifying Repeat Mode` example for mode details. Default is `virtual`.
 *
 * @usage
 * ### Basic Example
 * <hljs lang="html">
 *   <md-autocomplete
 *       md-selected-item="selectedItem"
 *       md-search-text="searchText"
 *       md-items="item in getMatches(searchText)"
 *       md-item-text="item.display">
 *     <span md-highlight-text="searchText">{{item.display}}</span>
 *   </md-autocomplete>
 * </hljs>
 *
 * ### Example with "not found" message
 * <hljs lang="html">
 * <md-autocomplete
 *     md-selected-item="selectedItem"
 *     md-search-text="searchText"
 *     md-items="item in getMatches(searchText)"
 *     md-item-text="item.display">
 *   <md-item-template>
 *     <span md-highlight-text="searchText">{{item.display}}</span>
 *   </md-item-template>
 *   <md-not-found>
 *     No matches found.
 *   </md-not-found>
 * </md-autocomplete>
 * </hljs>
 *
 * In this example, our code utilizes `md-item-template` and `md-not-found` to specify the
 *     different parts that make up our component.
 *
 * ### Clear button for the input
 * By default, the clear button is displayed when there is input. This aligns with the spec's
 * [Search Pattern](https://material.io/archive/guidelines/patterns/search.html#search-in-app-search).
 * In floating label mode, when `md-floating-label="My Label"` is applied, the clear button is not
 * displayed by default (see the spec's
 * [Autocomplete Text Field](https://material.io/archive/guidelines/components/text-fields.html#text-fields-layout)).
 *
 * Nevertheless, developers are able to explicitly toggle the clear button for all autocomplete
 * components with `md-clear-button`.
 *
 * <hljs lang="html">
 *   <md-autocomplete ... md-clear-button="true"></md-autocomplete>
 *   <md-autocomplete ... md-clear-button="false"></md-autocomplete>
 * </hljs>
 *
 * In previous versions, the clear button was always hidden when the component was disabled.
 * This changed in `1.1.5` to give the developer control of this behavior. This example
 * will hide the clear button only when the component is disabled.
 *
 * <hljs lang="html">
 *   <md-autocomplete ... ng-disabled="disabled" md-clear-button="!disabled"></md-autocomplete>
 * </hljs>
 *
 * ### Example with validation
 * <hljs lang="html">
 * <form name="autocompleteForm">
 *   <md-autocomplete
 *       required
 *       md-input-name="autocomplete"
 *       md-selected-item="selectedItem"
 *       md-search-text="searchText"
 *       md-items="item in getMatches(searchText)"
 *       md-item-text="item.display">
 *     <md-item-template>
 *       <span md-highlight-text="searchText">{{item.display}}</span>
 *     </md-item-template>
 *     <div ng-messages="autocompleteForm.autocomplete.$error">
 *       <div ng-message="required">This field is required</div>
 *     </div>
 *   </md-autocomplete>
 * </form>
 * </hljs>
 *
 * In this example, our code utilizes `md-item-template` and `ng-messages` to specify
 *     input validation for the field.
 *
 * ### Asynchronous Results
 * The autocomplete items expression also supports promises, which will resolve with the query
 * results.
 *
 * <hljs lang="js">
 *   function AppController($scope, $http) {
 *     $scope.query = function(searchText) {
 *       return $http
 *         .get(BACKEND_URL + '/items/' + searchText)
 *         .then(function(data) {
 *           // Map the response object to the data object.
 *           return data;
 *         });
 *     };
 *   }
 * </hljs>
 *
 * <hljs lang="html">
 *   <md-autocomplete
 *       md-selected-item="selectedItem"
 *       md-search-text="searchText"
 *       md-items="item in query(searchText)">
 *     <md-item-template>
 *       <span md-highlight-text="searchText">{{item}}</span>
 *     </md-item-template>
 * </md-autocomplete>
 * </hljs>
 *
 * ### Specifying Repeat Mode
 * You can use `md-mode` to specify whether to use standard or virtual lists for
 * rendering autocomplete options.
 * The `md-mode` accepts two values:
 * - `virtual` (default) Uses `md-virtual-repeat` to render list items. Virtual
 *    mode requires you to have consistent heights for all suggestions.
 * - `standard` uses `ng-repeat` to render list items. This allows you to have
 *    options of varying heights.
 *
 * Note that using 'standard' mode will require you to address any list
 * performance issues (e.g. pagination) separately within your application.
 *
 * <hljs lang="html">
 *   <md-autocomplete
 *       md-selected-item="selectedItem"
 *       md-search-text="searchText"
 *       md-items="item in getMatches(searchText)"
 *       md-item-text="item.display"
 *       md-mode="standard">
 *     <span md-highlight-text="searchText">{{item.display}}</span>
 *   </md-autocomplete>
 * </hljs>
 */
function MdAutocomplete ($$mdSvgRegistry) {
  var REPEAT_STANDARD = 'standard';
  var REPEAT_VIRTUAL = 'virtual';
  var REPEAT_MODES = [REPEAT_STANDARD, REPEAT_VIRTUAL];

  /** get a valid repeat mode from an md-mode attribute string. */
  function getRepeatMode(modeStr) {
    if (!modeStr) { return REPEAT_VIRTUAL; }
    modeStr = modeStr.toLowerCase();
    return  REPEAT_MODES.indexOf(modeStr) > -1 ? modeStr : REPEAT_VIRTUAL;
  }

  return {
    controller:   'MdAutocompleteCtrl',
    controllerAs: '$mdAutocompleteCtrl',
    scope:        {
      inputName:          '@mdInputName',
      inputMinlength:     '@mdInputMinlength',
      inputMaxlength:     '@mdInputMaxlength',
      searchText:         '=?mdSearchText',
      selectedItem:       '=?mdSelectedItem',
      itemsExpr:          '@mdItems',
      itemText:           '&mdItemText',
      placeholder:        '@placeholder',
      inputAriaDescribedBy: '@?inputAriaDescribedby',
      inputAriaLabelledBy: '@?inputAriaLabelledby',
      inputAriaLabel:     '@?inputAriaLabel',
      noCache:            '=?mdNoCache',
      requireMatch:       '=?mdRequireMatch',
      selectOnMatch:      '=?mdSelectOnMatch',
      matchInsensitive:   '=?mdMatchCaseInsensitive',
      itemChange:         '&?mdSelectedItemChange',
      textChange:         '&?mdSearchTextChange',
      minLength:          '=?mdMinLength',
      delay:              '=?mdDelay',
      autofocus:          '=?mdAutofocus',
      floatingLabel:      '@?mdFloatingLabel',
      autoselect:         '=?mdAutoselect',
      menuClass:          '@?mdMenuClass',
      menuContainerClass: '@?mdMenuContainerClass',
      inputClass:         '@?mdInputClass',
      inputId:            '@?mdInputId',
      escapeOptions:      '@?mdEscapeOptions',
      dropdownItems:      '=?mdDropdownItems',
      dropdownPosition:   '@?mdDropdownPosition',
      clearButton:        '=?mdClearButton',
      selectedMessage:    '@?mdSelectedMessage',
      mdMode: '=?mdMode'
    },
    compile: function(tElement, tAttrs) {
      var attributes = ['md-select-on-focus', 'md-no-asterisk', 'ng-trim', 'ng-pattern'];
      var input = tElement.find('input');

      attributes.forEach(function(attribute) {
        var attrValue = tAttrs[tAttrs.$normalize(attribute)];

        if (attrValue !== null) {
          input.attr(attribute, attrValue);
        }
      });

      return function(scope, element, attrs, ctrl) {
        // Retrieve the state of using a md-not-found template by using our attribute, which will
        // be added to the element in the template function.
        ctrl.hasNotFound = !!element.attr('md-has-not-found');

        // By default the inset autocomplete should show the clear button when not explicitly
        // overwritten or in floating label mode.
        if (!angular.isDefined(attrs.mdClearButton) && !scope.floatingLabel) {
          scope.clearButton = true;
        }

        scope.mdMode = getRepeatMode(attrs.mdMode);

        // Stop click events from bubbling up to the document and triggering a flicker of the
        // options panel while still supporting ng-click to be placed on md-autocomplete.
        element.on('click touchstart touchend', function(event) {
          event.stopPropagation();
        });
      };
    },
    template: function (element, attr) {
      var noItemsTemplate = getNoItemsTemplate(),
          itemTemplate    = getItemTemplate(),
          leftover        = element.html(),
          tabindex        = attr.tabindex;

      // Set our attribute for the link function above which runs later.
      // We will set an attribute, because otherwise the stored variables will be trashed when
      // removing the element is hidden while retrieving the template. For example when using ngIf.
      if (noItemsTemplate) element.attr('md-has-not-found', true);

      // Always set our tabindex of the autocomplete directive to -1, because our input
      // will hold the actual tabindex.
      element.attr('tabindex', '-1');

      return '\
        <md-autocomplete-wrap\
            ng-class="{ \'md-whiteframe-z1\': !floatingLabel, \
                        \'md-menu-showing\': !$mdAutocompleteCtrl.hidden, \
                        \'md-show-clear-button\': !!clearButton }">\
          ' + getInputElement() + '\
          ' + getClearButton() + '\
          <md-progress-linear\
              class="' + (attr.mdFloatingLabel ? 'md-inline' : '') + '"\
              ng-if="$mdAutocompleteCtrl.loadingIsVisible()"\
              md-mode="indeterminate"></md-progress-linear>\
          ' + getContainer(attr.mdMenuContainerClass, attr.mdMode) + '\
            <ul class="md-autocomplete-suggestions"\
                ng-class="::menuClass"\
                id="ul-{{$mdAutocompleteCtrl.id}}"\
                ng-mouseup="$mdAutocompleteCtrl.focusInput()"\
                role="listbox">\
              <li class="md-autocomplete-suggestion" ' + getRepeatType(attr.mdMode) + ' ="item in $mdAutocompleteCtrl.matches"\
                  ng-class="{ selected: $index === $mdAutocompleteCtrl.index }"\
                  ng-attr-id="{{\'md-option-\' + $mdAutocompleteCtrl.id + \'-\' + $index}}"\
                  ng-click="$mdAutocompleteCtrl.select($index)"\
                  role="option"\
                  aria-setsize="{{$mdAutocompleteCtrl.matches.length}}"\
                  aria-posinset="{{$index+1}}"\
                  aria-selected="{{$index === $mdAutocompleteCtrl.index ? true : false}}" \
                  md-extra-name="$mdAutocompleteCtrl.itemName">\
                  ' + itemTemplate + '\
                  </li>' + noItemsTemplate + '\
            </ul>\
          '  + getContainerClosingTags(attr.mdMode) + '\
        </md-autocomplete-wrap>';

      function getItemTemplate() {
        var templateTag = element.find('md-item-template').detach(),
            html = templateTag.length ? templateTag.html() : element.html();
        if (!templateTag.length) element.empty();
        return '<md-autocomplete-parent-scope md-autocomplete-replace>' + html +
               '</md-autocomplete-parent-scope>';
      }

      function getNoItemsTemplate() {
        var templateTag = element.find('md-not-found').detach(),
            template = templateTag.length ? templateTag.html() : '';
        return template
            ? '<li ng-if="$mdAutocompleteCtrl.notFoundVisible()" class="md-autocomplete-suggestion"\
                         md-autocomplete-parent-scope>' + template + '</li>'
            : '';
      }

      function getContainer(menuContainerClass, repeatMode) {
        // prepend a space if needed
        menuContainerClass = menuContainerClass ? ' ' + menuContainerClass : '';

        if (isVirtualRepeatDisabled(repeatMode)) {
          return '\
            <div \
                ng-hide="$mdAutocompleteCtrl.hidden"\
                class="md-standard-list-container md-autocomplete-suggestions-container md-whiteframe-z1' + menuContainerClass + '"\
                ng-class="{ \'md-not-found\': $mdAutocompleteCtrl.notFoundVisible() }"\
                ng-mouseenter="$mdAutocompleteCtrl.listEnter()"\
                ng-mouseleave="$mdAutocompleteCtrl.listLeave()"\
                role="presentation">\
              <div class="md-standard-list-scroller" role="presentation">';
        }

        return '\
          <md-virtual-repeat-container\
              md-auto-shrink\
              md-auto-shrink-min="1"\
              ng-hide="$mdAutocompleteCtrl.hidden"\
              class="md-virtual-repeat-container md-autocomplete-suggestions-container md-whiteframe-z1' + menuContainerClass + '"\
              ng-class="{ \'md-not-found\': $mdAutocompleteCtrl.notFoundVisible() }"\
              ng-mouseenter="$mdAutocompleteCtrl.listEnter()"\
              ng-mouseleave="$mdAutocompleteCtrl.listLeave()"\
              role="presentation">';
      }

      function getContainerClosingTags(repeatMode) {
        return isVirtualRepeatDisabled(repeatMode) ?
            '   </div>\
              </div>\
            </div>' : '</md-virtual-repeat-container>';
      }

      function getRepeatType(repeatMode) {
        return isVirtualRepeatDisabled(repeatMode)  ?
          'ng-repeat' : 'md-virtual-repeat';
      }

      function isVirtualRepeatDisabled(repeatMode) {
        // ensure we have a valid repeat mode
        var correctedRepeatMode = getRepeatMode(repeatMode);
        return correctedRepeatMode !== REPEAT_VIRTUAL;
      }

      function getInputElement () {
        if (attr.mdFloatingLabel) {
          return '\
            <md-input-container ng-if="floatingLabel">\
              <label>{{floatingLabel}}</label>\
              <input type="text"\
                ' + (tabindex != null ? 'tabindex="' + tabindex + '"' : '') + '\
                id="{{inputId || \'fl-input-\' + $mdAutocompleteCtrl.id}}"\
                name="{{inputName || \'fl-input-\' + $mdAutocompleteCtrl.id }}"\
                ng-class="::inputClass"\
                autocomplete="off"\
                ng-required="$mdAutocompleteCtrl.isRequired"\
                ng-readonly="$mdAutocompleteCtrl.isReadonly"\
                ng-minlength="inputMinlength"\
                ng-maxlength="inputMaxlength"\
                ng-disabled="$mdAutocompleteCtrl.isDisabled"\
                ng-model="$mdAutocompleteCtrl.scope.searchText"\
                ng-model-options="{ allowInvalid: true }"\
                ng-mousedown="$mdAutocompleteCtrl.focusInput()"\
                ng-keydown="$mdAutocompleteCtrl.keydown($event)"\
                ng-blur="$mdAutocompleteCtrl.blur($event)"\
                ng-focus="$mdAutocompleteCtrl.focus($event)"\
                aria-label="{{floatingLabel}}"\
                ng-attr-aria-autocomplete="{{$mdAutocompleteCtrl.isDisabled ? undefined : \'list\'}}"\
                ng-attr-role="{{$mdAutocompleteCtrl.isDisabled ? undefined : \'combobox\'}}"\
                aria-haspopup="{{!$mdAutocompleteCtrl.isDisabled}}"\
                aria-expanded="{{!$mdAutocompleteCtrl.hidden}}"\
                ng-attr-aria-owns="{{$mdAutocompleteCtrl.hidden || $mdAutocompleteCtrl.isDisabled ? undefined : \'ul-\' + $mdAutocompleteCtrl.id}}"\
                ng-attr-aria-activedescendant="{{!$mdAutocompleteCtrl.hidden && $mdAutocompleteCtrl.activeOption ? $mdAutocompleteCtrl.activeOption : undefined}}">\
              <div md-autocomplete-parent-scope md-autocomplete-replace>' + leftover + '</div>\
            </md-input-container>';
        } else {
          return '\
            <input type="text"\
              ' + (tabindex != null ? 'tabindex="' + tabindex + '"' : '') + '\
              id="{{inputId || \'input-\' + $mdAutocompleteCtrl.id}}"\
              name="{{inputName || \'input-\' + $mdAutocompleteCtrl.id }}"\
              ng-class="::inputClass"\
              ng-if="!floatingLabel"\
              autocomplete="off"\
              ng-required="$mdAutocompleteCtrl.isRequired"\
              ng-disabled="$mdAutocompleteCtrl.isDisabled"\
              ng-readonly="$mdAutocompleteCtrl.isReadonly"\
              ng-minlength="inputMinlength"\
              ng-maxlength="inputMaxlength"\
              ng-model="$mdAutocompleteCtrl.scope.searchText"\
              ng-mousedown="$mdAutocompleteCtrl.focusInput()"\
              ng-keydown="$mdAutocompleteCtrl.keydown($event)"\
              ng-blur="$mdAutocompleteCtrl.blur($event)"\
              ng-focus="$mdAutocompleteCtrl.focus($event)"\
              placeholder="{{placeholder}}"\
              aria-label="{{placeholder}}"\
              ng-attr-aria-autocomplete="{{$mdAutocompleteCtrl.isDisabled ? undefined : \'list\'}}"\
              ng-attr-role="{{$mdAutocompleteCtrl.isDisabled ? undefined : \'combobox\'}}"\
              aria-haspopup="{{!$mdAutocompleteCtrl.isDisabled}}"\
              aria-expanded="{{!$mdAutocompleteCtrl.hidden}}"\
              ng-attr-aria-owns="{{$mdAutocompleteCtrl.hidden || $mdAutocompleteCtrl.isDisabled ? undefined : \'ul-\' + $mdAutocompleteCtrl.id}}"\
              ng-attr-aria-activedescendant="{{!$mdAutocompleteCtrl.hidden && $mdAutocompleteCtrl.activeOption ? $mdAutocompleteCtrl.activeOption : undefined}}">';
        }
      }

      function getClearButton() {
        return '' +
          '<button ' +
              'type="button" ' +
              'aria-label="Clear Input" ' +
              'tabindex="0" ' +
              'ng-if="clearButton && $mdAutocompleteCtrl.scope.searchText" ' +
              'ng-click="$mdAutocompleteCtrl.clear($event)">' +
            '<md-icon md-svg-src="' + $$mdSvgRegistry.mdClose + '"></md-icon>' +
          '</button>';
        }
    }
  };
}

})();
(function(){
"use strict";


MdAutocompleteItemScopeDirective.$inject = ["$compile", "$mdUtil"];angular
  .module('material.components.autocomplete')
  .directive('mdAutocompleteParentScope', MdAutocompleteItemScopeDirective);

function MdAutocompleteItemScopeDirective($compile, $mdUtil) {
  return {
    restrict: 'AE',
    compile: compile,
    terminal: true,
    transclude: 'element'
  };

  function compile(tElement, tAttr, transclude) {
    return function postLink(scope, element, attr) {
      var ctrl = scope.$mdAutocompleteCtrl;
      var newScope = ctrl.parent.$new();
      var itemName = ctrl.itemName;

      // Watch for changes to our scope's variables and copy them to the new scope
      watchVariable('$index', '$index');
      watchVariable('item', itemName);

      // Ensure that $digest calls on our scope trigger $digest on newScope.
      connectScopes();

      // Link the element against newScope.
      transclude(newScope, function(clone) {
        element.after(clone);
      });

      /**
       * Creates a watcher for variables that are copied from the parent scope
       * @param variable
       * @param alias
       */
      function watchVariable(variable, alias) {
        newScope[alias] = scope[variable];

        scope.$watch(variable, function(value) {
          $mdUtil.nextTick(function() {
            newScope[alias] = value;
          });
        });
      }

      /**
       * Creates watchers on scope and newScope that ensure that for any
       * $digest of scope, newScope is also $digested.
       */
      function connectScopes() {
        var scopeDigesting = false;
        var newScopeDigesting = false;

        scope.$watch(function() {
          if (newScopeDigesting || scopeDigesting) {
            return;
          }

          scopeDigesting = true;
          scope.$$postDigest(function() {
            if (!newScopeDigesting) {
              newScope.$digest();
            }

            scopeDigesting = newScopeDigesting = false;
          });
        });

        newScope.$watch(function() {
          newScopeDigesting = true;
        });
      }
    };
  }
}
})();
(function(){
"use strict";


MdHighlightCtrl.$inject = ["$scope", "$element", "$attrs", "$mdUtil"];angular
    .module('material.components.autocomplete')
    .controller('MdHighlightCtrl', MdHighlightCtrl);

function MdHighlightCtrl ($scope, $element, $attrs, $mdUtil) {
  this.$scope = $scope;
  this.$element = $element;
  this.$attrs = $attrs;
  this.$mdUtil = $mdUtil;

  // Cache the Regex to avoid rebuilding each time.
  this.regex = null;
}

MdHighlightCtrl.prototype.init = function(unsafeTermFn, unsafeContentFn) {

  this.flags = this.$attrs.mdHighlightFlags || '';

  this.unregisterFn = this.$scope.$watch(function($scope) {
    return {
      term: unsafeTermFn($scope),
      contentText: unsafeContentFn($scope)
    };
  }.bind(this), this.onRender.bind(this), true);

  this.$element.on('$destroy', this.unregisterFn);
};

/**
 * Triggered once a new change has been recognized and the highlighted
 * text needs to be updated.
 */
MdHighlightCtrl.prototype.onRender = function(state, prevState) {

  var contentText = state.contentText;

  /* Update the regex if it's outdated, because we don't want to rebuilt it constantly. */
  if (this.regex === null || state.term !== prevState.term) {
    this.regex = this.createRegex(state.term, this.flags);
  }

  /* If a term is available apply the regex to the content */
  if (state.term) {
    this.applyRegex(contentText);
  } else {
    this.$element.text(contentText);
  }

};

/**
 * Decomposes the specified text into different tokens (whether match or not).
 * Breaking down the string guarantees proper XSS protection due to the native browser
 * escaping of unsafe text.
 */
MdHighlightCtrl.prototype.applyRegex = function(text) {
  var tokens = this.resolveTokens(text);

  this.$element.empty();

  tokens.forEach(function (token) {

    if (token.isMatch) {
      var tokenEl = angular.element('<span class="highlight">').text(token.text);

      this.$element.append(tokenEl);
    } else {
      this.$element.append(document.createTextNode(token));
    }

  }.bind(this));

};

  /**
 * Decomposes the specified text into different tokens by running the regex against the text.
 */
MdHighlightCtrl.prototype.resolveTokens = function(string) {
  var tokens = [];
  var lastIndex = 0;

  // Use replace here, because it supports global and single regular expressions at same time.
  string.replace(this.regex, function(match, index) {
    appendToken(lastIndex, index);

    tokens.push({
      text: match,
      isMatch: true
    });

    lastIndex = index + match.length;
  });

  // Append the missing text as a token.
  appendToken(lastIndex);

  return tokens;

  function appendToken(from, to) {
    var targetText = string.slice(from, to);
    targetText && tokens.push(targetText);
  }
};

/** Creates a regex for the specified text with the given flags. */
MdHighlightCtrl.prototype.createRegex = function(term, flags) {
  var startFlag = '', endFlag = '';
  var regexTerm = this.$mdUtil.sanitize(term);

  if (flags.indexOf('^') >= 0) startFlag = '^';
  if (flags.indexOf('$') >= 0) endFlag = '$';

  return new RegExp(startFlag + regexTerm + endFlag, flags.replace(/[$^]/g, ''));
};

})();
(function(){
"use strict";


MdHighlight.$inject = ["$interpolate", "$parse"];angular
    .module('material.components.autocomplete')
    .directive('mdHighlightText', MdHighlight);

/**
 * @ngdoc directive
 * @name mdHighlightText
 * @module material.components.autocomplete
 *
 * @description
 * The `md-highlight-text` directive allows you to specify text that should be highlighted within
 *     an element.  Highlighted text will be wrapped in `<span class="highlight"></span>` which can
 *     be styled through CSS.  Please note that child elements may not be used with this directive.
 *
 * @param {string} md-highlight-text A model to be searched for
 * @param {string=} md-highlight-flags A list of flags (loosely based on JavaScript RexExp flags).
 * #### **Supported flags**:
 * - `g`: Find all matches within the provided text
 * - `i`: Ignore case when searching for matches
 * - `$`: Only match if the text ends with the search term
 * - `^`: Only match if the text begins with the search term
 *
 * @usage
 * <hljs lang="html">
 * <input placeholder="Enter a search term..." ng-model="searchTerm" type="text" />
 * <ul>
 *   <li ng-repeat="result in results" md-highlight-text="searchTerm" md-highlight-flags="i">
 *     {{result.text}}
 *   </li>
 * </ul>
 * </hljs>
 */

function MdHighlight ($interpolate, $parse) {
  return {
    terminal: true,
    controller: 'MdHighlightCtrl',
    compile: function mdHighlightCompile(tElement, tAttr) {
      var termExpr = $parse(tAttr.mdHighlightText);
      var unsafeContentExpr = $interpolate(tElement.html());

      return function mdHighlightLink(scope, element, attr, ctrl) {
        ctrl.init(termExpr, unsafeContentExpr);
      };
    }
  };
}

})();
(function(){
"use strict";

/*
 * @ngdoc module
 * @name material.components.backdrop
 * @description Backdrop
 */

/**
 * @ngdoc directive
 * @name mdBackdrop
 * @module material.components.backdrop
 *
 * @restrict E
 *
 * @description
 * `<md-backdrop>` is a backdrop element used by other components, such as dialog and bottom sheet.
 * Apply class `opaque` to make the backdrop use the theme backdrop color.
 *
 */

angular
  .module('material.components.backdrop', ['material.core'])
  .directive('mdBackdrop', ["$mdTheming", "$mdUtil", "$animate", "$rootElement", "$window", "$log", "$$rAF", "$document", function BackdropDirective($mdTheming, $mdUtil, $animate, $rootElement, $window, $log, $$rAF, $document) {
    var ERROR_CSS_POSITION = '<md-backdrop> may not work properly in a scrolled, static-positioned parent container.';

    return {
      restrict: 'E',
      link: postLink
    };

    function postLink(scope, element, attrs) {
      // backdrop may be outside the $rootElement, tell ngAnimate to animate regardless
      if ($animate.pin) $animate.pin(element, $rootElement);

      var bodyStyles;

      $$rAF(function() {
        // If body scrolling has been disabled using mdUtil.disableBodyScroll(),
        // adjust the 'backdrop' height to account for the fixed 'body' top offset.
        // Note that this can be pretty expensive and is better done inside the $$rAF.
        bodyStyles = $window.getComputedStyle($document[0].body);

        if (bodyStyles.position === 'fixed') {
          var resizeHandler = $mdUtil.debounce(function(){
            bodyStyles = $window.getComputedStyle($document[0].body);
            resize();
          }, 60, null, false);

          resize();
          angular.element($window).on('resize', resizeHandler);

          scope.$on('$destroy', function() {
            angular.element($window).off('resize', resizeHandler);
          });
        }

        // Often $animate.enter() is used to append the backDrop element
        // so let's wait until $animate is done...
        var parent = element.parent();

        if (parent.length) {
          if (parent[0].nodeName === 'BODY') {
            element.css('position', 'fixed');
          }

          var styles = $window.getComputedStyle(parent[0]);

          if (styles.position === 'static') {
            // backdrop uses position:absolute and will not work properly with parent position:static (default)
            $log.warn(ERROR_CSS_POSITION);
          }

          // Only inherit the parent if the backdrop has a parent.
          $mdTheming.inherit(element, parent);
        }
      });

      function resize() {
        var viewportHeight = parseInt(bodyStyles.height, 10) + Math.abs(parseInt(bodyStyles.top, 10));
        element.css('height', viewportHeight + 'px');
      }
    }

  }]);

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.bottomSheet
 * @description
 * BottomSheet
 */
MdBottomSheetDirective.$inject = ["$mdBottomSheet"];
MdBottomSheetProvider.$inject = ["$$interimElementProvider"];
angular
  .module('material.components.bottomSheet', [
    'material.core',
    'material.components.backdrop'
  ])
  .directive('mdBottomSheet', MdBottomSheetDirective)
  .provider('$mdBottomSheet', MdBottomSheetProvider);

/* @ngInject */
function MdBottomSheetDirective($mdBottomSheet) {
  return {
    restrict: 'E',
    link : function postLink(scope, element) {
      element.addClass('_md');     // private md component indicator for styling

      // When navigation force destroys an interimElement, then
      // listen and $destroy() that interim instance...
      scope.$on('$destroy', function() {
        $mdBottomSheet.destroy();
      });
    }
  };
}


/**
 * @ngdoc service
 * @name $mdBottomSheet
 * @module material.components.bottomSheet
 *
 * @description
 * `$mdBottomSheet` opens a bottom sheet over the app and provides a simple promise API.
 *
 * ## Restrictions
 *
 * - The bottom sheet's template must have an outer `<md-bottom-sheet>` element.
 * - Add the `md-grid` class to the bottom sheet for a grid layout.
 * - Add the `md-list` class to the bottom sheet for a list layout.
 *
 * @usage
 * <hljs lang="html">
 * <div ng-controller="MyController">
 *   <md-button ng-click="openBottomSheet()">
 *     Open a Bottom Sheet!
 *   </md-button>
 * </div>
 * </hljs>
 * <hljs lang="js">
 * var app = angular.module('app', ['ngMaterial']);
 * app.controller('MyController', function($scope, $mdBottomSheet) {
 *   $scope.openBottomSheet = function() {
 *     $mdBottomSheet.show({
 *       template: '<md-bottom-sheet>' +
 *       'Hello! <md-button ng-click="closeBottomSheet()">Close</md-button>' +
 *       '</md-bottom-sheet>'
 *     })
 *
 *     // Fires when the hide() method is used
 *     .then(function() {
 *       console.log('You clicked the button to close the bottom sheet!');
 *     })
 *
 *     // Fires when the cancel() method is used
 *     .catch(function() {
 *       console.log('You hit escape or clicked the backdrop to close.');
 *     });
 *   };
 *
 *   $scope.closeBottomSheet = function($scope, $mdBottomSheet) {
 *     $mdBottomSheet.hide();
 *   }
 *
 * });
 * </hljs>
 *
 * ### Custom Presets
 * Developers are also able to create their own preset, which can be easily used without repeating
 * their options each time.
 *
 * <hljs lang="js">
 *   $mdBottomSheetProvider.addPreset('testPreset', {
 *     options: function() {
 *       return {
 *         template:
 *           '<md-bottom-sheet>' +
 *             'This is a custom preset' +
 *           '</md-bottom-sheet>',
 *         controllerAs: 'bottomSheet',
 *         bindToController: true,
 *         clickOutsideToClose: true,
 *         escapeToClose: true
 *       };
 *     }
 *   });
 * </hljs>
 *
 * After you create your preset during the config phase, you can easily access it.
 *
 * <hljs lang="js">
 *   $mdBottomSheet.show(
 *     $mdBottomSheet.testPreset()
 *   );
 * </hljs>
 */

 /**
 * @ngdoc method
 * @name $mdBottomSheet#show
 *
 * @description
 * Show a bottom sheet with the specified options.
 *
 * <em><b>Note:</b> You should <b>always</b> provide a `.catch()` method in case the user hits the
 * `esc` key or clicks the background to close. In this case, the `cancel()` method will
 * automatically be called on the bottom sheet which will `reject()` the promise. See the @usage
 * section above for an example.
 *
 * Newer versions of Angular will throw a `Possibly unhandled rejection` exception if you forget
 * this.</em>
 *
 * @param {object} optionsOrPreset Either provide an `$mdBottomSheetPreset` defined during the config phase or
 * an options object, with the following properties:
 *
 *   - `templateUrl` - `{string=}`: The url of an html template file that will
 *   be used as the content of the bottom sheet. Restrictions: the template must
 *   have an outer `md-bottom-sheet` element.
 *   - `template` - `{string=}`: Same as templateUrl, except this is an actual
 *   template string.
 *   - `scope` - `{object=}`: the scope to link the template / controller to. If none is specified, it will create a new child scope.
 *     This scope will be destroyed when the bottom sheet is removed unless `preserveScope` is set to true.
 *   - `preserveScope` - `{boolean=}`: whether to preserve the scope when the element is removed. Default is false
 *   - `controller` - `{string=}`: The controller to associate with this bottom sheet.
 *   - `locals` - `{string=}`: An object containing key/value pairs. The keys will
 *   be used as names of values to inject into the controller. For example,
 *   `locals: {three: 3}` would inject `three` into the controller with the value
 *   of 3.
 *   - `clickOutsideToClose` - `{boolean=}`: Whether the user can click outside the bottom sheet to
 *     close it. Default true.
 *   - `bindToController` - `{boolean=}`: When set to true, the locals will be bound to the controller instance.
 *   - `disableBackdrop` - `{boolean=}`: When set to true, the bottomsheet will not show a backdrop.
 *   - `escapeToClose` - `{boolean=}`: Whether the user can press escape to close the bottom sheet.
 *     Default true.
 *   - `isLockedOpen` - `{boolean=}`: Disables all default ways of closing the bottom sheet. **Note:** this will override
 *     the `clickOutsideToClose` and `escapeToClose` options, leaving only the `hide` and `cancel`
 *     methods as ways of closing the bottom sheet. Defaults to false.
 *   - `resolve` - `{object=}`: Similar to locals, except it takes promises as values
 *   and the bottom sheet will not open until the promises resolve.
 *   - `controllerAs` - `{string=}`: An alias to assign the controller to on the scope.
 *   - `parent` - `{element=}`: The element to append the bottom sheet to. The `parent` may be a `function`, `string`,
 *   `object`, or null. Defaults to appending to the body of the root element (or the root element) of the application.
 *   e.g. angular.element(document.getElementById('content')) or "#content"
 *   - `disableParentScroll` - `{boolean=}`: Whether to disable scrolling while the bottom sheet is open.
 *     Default true.
 *
 * @returns {promise} A promise that can be resolved with `$mdBottomSheet.hide()` or
 * rejected with `$mdBottomSheet.cancel()`.
 */

/**
 * @ngdoc method
 * @name $mdBottomSheet#hide
 *
 * @description
 * Hide the existing bottom sheet and resolve the promise returned from
 * `$mdBottomSheet.show()`. This call will close the most recently opened/current bottom sheet (if
 * any).
 *
 * <em><b>Note:</b> Use a `.then()` on your `.show()` to handle this callback.</em>
 *
 * @param {*=} response An argument for the resolved promise.
 *
 */

/**
 * @ngdoc method
 * @name $mdBottomSheet#cancel
 *
 * @description
 * Hide the existing bottom sheet and reject the promise returned from
 * `$mdBottomSheet.show()`.
 *
 * <em><b>Note:</b> Use a `.catch()` on your `.show()` to handle this callback.</em>
 *
 * @param {*=} response An argument for the rejected promise.
 *
 */

function MdBottomSheetProvider($$interimElementProvider) {
  // how fast we need to flick down to close the sheet, pixels/ms
  bottomSheetDefaults.$inject = ["$animate", "$mdConstant", "$mdUtil", "$mdTheming", "$mdBottomSheet", "$rootElement", "$mdGesture", "$log"];
  var CLOSING_VELOCITY = 0.5;
  var PADDING = 80; // same as css

  return $$interimElementProvider('$mdBottomSheet')
    .setDefaults({
      methods: ['disableParentScroll', 'escapeToClose', 'clickOutsideToClose'],
      options: bottomSheetDefaults
    });

  /* @ngInject */
  function bottomSheetDefaults($animate, $mdConstant, $mdUtil, $mdTheming, $mdBottomSheet, $rootElement,
                               $mdGesture, $log) {
    var backdrop;

    return {
      themable: true,
      onShow: onShow,
      onRemove: onRemove,
      disableBackdrop: false,
      escapeToClose: true,
      clickOutsideToClose: true,
      disableParentScroll: true,
      isLockedOpen: false
    };


    function onShow(scope, element, options, controller) {

      element = $mdUtil.extractElementByName(element, 'md-bottom-sheet');

      // prevent tab focus or click focus on the bottom-sheet container
      element.attr('tabindex', '-1');

      // Once the md-bottom-sheet has `ng-cloak` applied on his template the opening animation will not work properly.
      // This is a very common problem, so we have to notify the developer about this.
      if (element.hasClass('ng-cloak')) {
        var message = '$mdBottomSheet: using `<md-bottom-sheet ng-cloak>` will affect the bottom-sheet opening animations.';
        $log.warn(message, element[0]);
      }

      if (options.isLockedOpen) {
        options.clickOutsideToClose = false;
        options.escapeToClose = false;
      } else {
        options.cleanupGestures = registerGestures(element, options.parent);
      }

      if (!options.disableBackdrop) {
        // Add a backdrop that will close on click
        backdrop = $mdUtil.createBackdrop(scope, "md-bottom-sheet-backdrop md-opaque");

        // Prevent mouse focus on backdrop; ONLY programmatic focus allowed.
        // This allows clicks on backdrop to propagate to the $rootElement and
        // ESC key events to be detected properly.
        backdrop[0].tabIndex = -1;

        if (options.clickOutsideToClose) {
          backdrop.on('click', function() {
            $mdUtil.nextTick($mdBottomSheet.cancel, true);
          });
        }

        $mdTheming.inherit(backdrop, options.parent);

        $animate.enter(backdrop, options.parent, null);
      }

      $mdTheming.inherit(element, options.parent);

      if (options.disableParentScroll) {
        options.restoreScroll = $mdUtil.disableScrollAround(element, options.parent);
      }

      return $animate.enter(element, options.parent, backdrop)
        .then(function() {
          var focusable = $mdUtil.findFocusTarget(element) || angular.element(
            element[0].querySelector('button') ||
            element[0].querySelector('a') ||
            element[0].querySelector($mdUtil.prefixer('ng-click', true))
          ) || backdrop;

          if (options.escapeToClose) {
            options.rootElementKeyupCallback = function(e) {
              if (e.keyCode === $mdConstant.KEY_CODE.ESCAPE) {
                $mdUtil.nextTick($mdBottomSheet.cancel, true);
              }
            };

            $rootElement.on('keyup', options.rootElementKeyupCallback);
            focusable && focusable.focus();
          }
        });

    }

    function onRemove(scope, element, options) {
      if (!options.disableBackdrop) $animate.leave(backdrop);

      return $animate.leave(element).then(function() {
        if (options.disableParentScroll) {
          options.restoreScroll();
          delete options.restoreScroll;
        }

        options.cleanupGestures && options.cleanupGestures();
      });
    }

    /**
     * Adds the drag gestures to the bottom sheet.
     * @param {angular.JQLite} element where CSS transitions will be applied
     * @param {angular.JQLite} parent used for registering gesture listeners
     * @return {Function} function that removes gesture listeners that were set up by
     *  registerGestures()
     */
    function registerGestures(element, parent) {
      var deregister = $mdGesture.register(parent, 'drag', { horizontal: false });
      parent.on('$md.dragstart', onDragStart)
        .on('$md.drag', onDrag)
        .on('$md.dragend', onDragEnd);

      return function cleanupGestures() {
        deregister();
        parent.off('$md.dragstart', onDragStart);
        parent.off('$md.drag', onDrag);
        parent.off('$md.dragend', onDragEnd);
      };

      function onDragStart() {
        // Disable transitions on transform so that it feels fast
        element.css($mdConstant.CSS.TRANSITION_DURATION, '0ms');
      }

      function onDrag(ev) {
        var transform = ev.pointer.distanceY;
        if (transform < 5) {
          // Slow down drag when trying to drag up, and stop after PADDING
          transform = Math.max(-PADDING, transform / 2);
        }
        element.css($mdConstant.CSS.TRANSFORM, 'translate3d(0,' + (PADDING + transform) + 'px,0)');
      }

      function onDragEnd(ev) {
        if (ev.pointer.distanceY > 0 &&
            (ev.pointer.distanceY > 20 || Math.abs(ev.pointer.velocityY) > CLOSING_VELOCITY)) {
          var distanceRemaining = element.prop('offsetHeight') - ev.pointer.distanceY;
          var transitionDuration = Math.min(distanceRemaining / ev.pointer.velocityY * 0.75, 500);
          element.css($mdConstant.CSS.TRANSITION_DURATION, transitionDuration + 'ms');
          $mdUtil.nextTick($mdBottomSheet.cancel, true);
        } else {
          element.css($mdConstant.CSS.TRANSITION_DURATION, '');
          element.css($mdConstant.CSS.TRANSFORM, '');
        }
      }
    }

  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.button
 * @description
 *
 * Button
 */
MdButtonDirective.$inject = ["$mdButtonInkRipple", "$mdTheming", "$mdAria", "$mdInteraction"];
MdAnchorDirective.$inject = ["$mdTheming"];
angular
    .module('material.components.button', ['material.core'])
    .directive('mdButton', MdButtonDirective)
    .directive('a', MdAnchorDirective);


/**
 * @private
 * @restrict E
 *
 * @description
 * `a` is an anchor directive used to inherit theme colors for md-primary, md-accent, etc.
 *
 * @usage
 *
 * <hljs lang="html">
 *  <md-content md-theme="myTheme">
 *    <a href="#chapter1" class="md-accent"></a>
 *  </md-content>
 * </hljs>
 */
function MdAnchorDirective($mdTheming) {
  return {
    restrict : 'E',
    link : function postLink(scope, element) {
      // Make sure to inherit theme so stand-alone anchors
      // support theme colors for md-primary, md-accent, etc.
      $mdTheming(element);
    }
  };
}


/**
 * @ngdoc directive
 * @name mdButton
 * @module material.components.button
 *
 * @restrict E
 *
 * @description
 * `<md-button>` is a button directive with optional ink ripples (default enabled).
 *
 * If you supply a `href` or `ng-href` attribute, it will become an `<a>` element. Otherwise, it
 * will become a `<button>` element. As per the
 * [Material Design specifications](https://material.google.com/style/color.html#color-color-palette)
 * the FAB button background is filled with the accent color [by default]. The primary color palette
 * may be used with the `md-primary` class.
 *
 * Developers can also change the color palette of the button, by using the following classes
 * - `md-primary`
 * - `md-accent`
 * - `md-warn`
 *
 * See for example
 *
 * <hljs lang="html">
 *   <md-button class="md-primary">Primary Button</md-button>
 * </hljs>
 *
 * Button can be also raised, which means that they will use the current color palette to fill the button.
 *
 * <hljs lang="html">
 *   <md-button class="md-accent md-raised">Raised and Accent Button</md-button>
 * </hljs>
 *
 * It is also possible to disable the focus effect on the button, by using the following markup.
 *
 * <hljs lang="html">
 *   <md-button class="md-no-focus">No Focus Style</md-button>
 * </hljs>
 *
 * @param {string=} aria-label Adds alternative text to button for accessibility, useful for icon buttons.
 * If no default text is found, a warning will be logged.
 * @param {boolean=} md-no-ink If present, disable ink ripple effects.
 * @param {string=} md-ripple-size Overrides the default ripple size logic. Options: `full`, `partial`, `auto`.
 * @param {expression=} ng-disabled Disable the button when the expression is truthy.
 * @param {expression=} ng-blur Expression evaluated when focus is removed from the button.
 *
 * @usage
 *
 * Regular buttons:
 *
 * <hljs lang="html">
 *  <md-button> Flat Button </md-button>
 *  <md-button href="http://google.com"> Flat link </md-button>
 *  <md-button class="md-raised"> Raised Button </md-button>
 *  <md-button ng-disabled="true"> Disabled Button </md-button>
 *  <md-button>
 *    <md-icon md-svg-src="your/icon.svg"></md-icon>
 *    Register Now
 *  </md-button>
 * </hljs>
 *
 * FAB buttons:
 *
 * <hljs lang="html">
 *  <md-button class="md-fab" aria-label="FAB">
 *    <md-icon md-svg-src="your/icon.svg"></md-icon>
 *  </md-button>
 *  <!-- mini-FAB -->
 *  <md-button class="md-fab md-mini" aria-label="Mini FAB">
 *    <md-icon md-svg-src="your/icon.svg"></md-icon>
 *  </md-button>
 *  <!-- Button with SVG Icon -->
 *  <md-button class="md-icon-button" aria-label="Custom Icon Button">
 *    <md-icon md-svg-icon="path/to/your.svg"></md-icon>
 *  </md-button>
 * </hljs>
 */
function MdButtonDirective($mdButtonInkRipple, $mdTheming, $mdAria, $mdInteraction) {

  return {
    restrict: 'EA',
    replace: true,
    transclude: true,
    template: getTemplate,
    link: postLink
  };

  function isAnchor(attr) {
    return angular.isDefined(attr.href) || angular.isDefined(attr.ngHref) || angular.isDefined(attr.ngLink) || angular.isDefined(attr.uiSref);
  }

  function getTemplate(element, attr) {
    if (isAnchor(attr)) {
      return '<a class="md-button" ng-transclude></a>';
    } else {
      // If buttons don't have type="button", they will submit forms automatically.
      var btnType = (typeof attr.type === 'undefined') ? 'button' : attr.type;
      return '<button class="md-button" type="' + btnType + '" ng-transclude></button>';
    }
  }

  function postLink(scope, element, attr) {
    $mdTheming(element);
    $mdButtonInkRipple.attach(scope, element);

    // Use async expect to support possible bindings in the button label
    $mdAria.expectWithoutText(element, 'aria-label');

    // For anchor elements, we have to set tabindex manually when the element is disabled.
    // We don't do this for md-nav-bar anchors as the component manages its own tabindex values.
    if (isAnchor(attr) && angular.isDefined(attr.ngDisabled) &&
        !element.hasClass('_md-nav-button')) {
      scope.$watch(attr.ngDisabled, function(isDisabled) {
        element.attr('tabindex', isDisabled ? -1 : 0);
      });
    }

    // disabling click event when disabled is true
    element.on('click', function(e){
      if (attr.disabled === true) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    });

    if (!element.hasClass('md-no-focus')) {

      element.on('focus', function() {

        // Only show the focus effect when being focused through keyboard interaction or programmatically
        if (!$mdInteraction.isUserInvoked() || $mdInteraction.getLastInteractionType() === 'keyboard') {
          element.addClass('md-focused');
        }

      });

      element.on('blur', function() {
        element.removeClass('md-focused');
      });
    }

  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.card
 *
 * @description
 * Card components.
 */
mdCardDirective.$inject = ["$mdTheming"];
angular.module('material.components.card', [
    'material.core'
  ])
  .directive('mdCard', mdCardDirective);


/**
 * @ngdoc directive
 * @name mdCard
 * @module material.components.card
 *
 * @restrict E
 *
 * @description
 * The `<md-card>` directive is a container element used within `<md-content>` containers.
 *
 * An image included as a direct descendant will fill the card's width. If you want to avoid this,
 * you can add the `md-image-no-fill` class to the parent element. The `<md-card-content>`
 * container will wrap text content and provide padding. An `<md-card-footer>` element can be
 * optionally included to put content flush against the bottom edge of the card.
 *
 * Action buttons can be included in an `<md-card-actions>` element, similar to `<md-dialog-actions>`.
 * You can then position buttons using layout attributes.
 *
 * Card is built with:
 * * `<md-card-header>` - Header for the card, holds avatar, text and squared image
 *  - `<md-card-avatar>` - Card avatar
 *    - `md-user-avatar` - Class for user image
 *    - `<md-icon>`
 *  - `<md-card-header-text>` - Contains elements for the card description
 *    - `md-title` - Class for the card title
 *    - `md-subhead` - Class for the card sub header
 * * `<img>` - Image for the card
 * * `<md-card-title>` - Card content title
 *  - `<md-card-title-text>`
 *    - `md-headline` - Class for the card content title
 *    - `md-subhead` - Class for the card content sub header
 *  - `<md-card-title-media>` - Squared image within the title
 *    - `md-media-sm` - Class for small image
 *    - `md-media-md` - Class for medium image
 *    - `md-media-lg` - Class for large image
 *    - `md-media-xl` - Class for extra large image
 * * `<md-card-content>` - Card content
 * * `<md-card-actions>` - Card actions
 *  - `<md-card-icon-actions>` - Icon actions
 *
 * Cards have constant width and variable heights; where the maximum height is limited to what can
 * fit within a single view on a platform, but it can temporarily expand as needed.
 *
 * @usage
 * ### Card with optional footer
 * <hljs lang="html">
 * <md-card>
 *  <img src="card-image.png" class="md-card-image" alt="image caption">
 *  <md-card-content>
 *    <h2>Card headline</h2>
 *    <p>Card content</p>
 *  </md-card-content>
 *  <md-card-footer>
 *    Card footer
 *  </md-card-footer>
 * </md-card>
 * </hljs>
 *
 * ### Card with actions
 * <hljs lang="html">
 * <md-card>
 *  <img src="card-image.png" class="md-card-image" alt="image caption">
 *  <md-card-content>
 *    <h2>Card headline</h2>
 *    <p>Card content</p>
 *  </md-card-content>
 *  <md-card-actions layout="row" layout-align="end center">
 *    <md-button>Action 1</md-button>
 *    <md-button>Action 2</md-button>
 *  </md-card-actions>
 * </md-card>
 * </hljs>
 *
 * ### Card with header, image, title actions and content
 * <hljs lang="html">
 * <md-card>
 *   <md-card-header>
 *     <md-card-avatar>
 *       <img class="md-user-avatar" src="avatar.png"/>
 *     </md-card-avatar>
 *     <md-card-header-text>
 *       <span class="md-title">Title</span>
 *       <span class="md-subhead">Sub header</span>
 *     </md-card-header-text>
 *   </md-card-header>
 *   <img ng-src="card-image.png" class="md-card-image" alt="image caption">
 *   <md-card-title>
 *     <md-card-title-text>
 *       <span class="md-headline">Card headline</span>
 *       <span class="md-subhead">Card subheader</span>
 *     </md-card-title-text>
 *   </md-card-title>
 *   <md-card-actions layout="row" layout-align="start center">
 *     <md-button>Action 1</md-button>
 *     <md-button>Action 2</md-button>
 *     <md-card-icon-actions>
 *       <md-button class="md-icon-button" aria-label="icon">
 *         <md-icon md-svg-icon="icon"></md-icon>
 *       </md-button>
 *     </md-card-icon-actions>
 *   </md-card-actions>
 *   <md-card-content>
 *     <p>
 *      Card content
 *     </p>
 *   </md-card-content>
 * </md-card>
 * </hljs>
 */
function mdCardDirective($mdTheming) {
  return {
    restrict: 'E',
    link: function ($scope, $element, attr) {
      $element.addClass('_md');     // private md component indicator for styling
      $mdTheming($element);
    }
  };
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.checkbox
 * @description Checkbox module!
 */
MdCheckboxDirective.$inject = ["inputDirective", "$mdAria", "$mdConstant", "$mdTheming", "$mdUtil", "$mdInteraction"];
angular
  .module('material.components.checkbox', ['material.core'])
  .directive('mdCheckbox', MdCheckboxDirective);

/**
 * @ngdoc directive
 * @name mdCheckbox
 * @module material.components.checkbox
 * @restrict E
 *
 * @description
 * The checkbox directive is used like the normal
 * [angular checkbox](https://docs.angularjs.org/api/ng/input/input%5Bcheckbox%5D).
 *
 * As per the [Material Design spec](https://material.io/archive/guidelines/style/color.html#color-color-palette)
 * the checkbox is in the accent color by default. The primary color palette may be used with
 * the `md-primary` class.
 *
 * @param {expression} ng-model Assignable angular expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {expression=} ng-true-value The value to which the expression should be set when selected.
 * @param {expression=} ng-false-value The value to which the expression should be set when not
 *    selected.
 * @param {expression=} ng-change Expression to be executed when the model value changes.
 * @param {boolean=} md-no-ink If present, disable ink ripple effects.
 * @param {string=} aria-label Adds label to checkbox for accessibility.
 *    Defaults to checkbox's text. If no default text is found, a warning will be logged.
 * @param {expression=} md-indeterminate This determines when the checkbox should be rendered as
 *    'indeterminate'. If a truthy expression or no value is passed in the checkbox renders in the
 *    md-indeterminate state. If falsy expression is passed in it just looks like a normal unchecked
 *    checkbox. The indeterminate, checked, and unchecked states are mutually exclusive. A box
 *    cannot be in any two states at the same time. Adding the 'md-indeterminate' attribute
 *    overrides any checked/unchecked rendering logic. When using the 'md-indeterminate' attribute
 *    use 'ng-checked' to define rendering logic instead of using 'ng-model'.
 * @param {expression=} ng-checked If this expression evaluates as truthy, the 'md-checked' css
 *    class is added to the checkbox and it will appear checked.
 *
 * @usage
 * <hljs lang="html">
 * <md-checkbox ng-model="isChecked" aria-label="Finished?">
 *   Finished ?
 * </md-checkbox>
 *
 * <md-checkbox md-no-ink ng-model="hasInk" aria-label="No Ink Effects">
 *   No Ink Effects
 * </md-checkbox>
 *
 * <md-checkbox ng-disabled="true" ng-model="isDisabled" aria-label="Disabled">
 *   Disabled
 * </md-checkbox>
 *
 * </hljs>
 *
 */
function MdCheckboxDirective(inputDirective, $mdAria, $mdConstant, $mdTheming, $mdUtil, $mdInteraction) {
  inputDirective = inputDirective[0];

  return {
    restrict: 'E',
    transclude: true,
    require: ['^?mdInputContainer', '?ngModel', '?^form'],
    priority: $mdConstant.BEFORE_NG_ARIA,
    template:
      '<div class="md-container" md-ink-ripple md-ink-ripple-checkbox>' +
        '<div class="md-icon"></div>' +
      '</div>' +
      '<div ng-transclude class="md-label"></div>',
    compile: compile
  };

  // **********************************************************
  // Private Methods
  // **********************************************************

  function compile (tElement, tAttrs) {
    tAttrs.$set('tabindex', tAttrs.tabindex || '0');
    tAttrs.$set('type', 'checkbox');
    tAttrs.$set('role', tAttrs.type);

    return  {
      pre: function(scope, element) {
        // Attach a click handler during preLink, in order to immediately stop propagation
        // (especially for ng-click) when the checkbox is disabled.
        element.on('click', function(e) {
          if (this.hasAttribute('disabled')) {
            e.stopImmediatePropagation();
          }
        });
      },
      post: postLink
    };

    function postLink(scope, element, attr, ctrls) {
      var isIndeterminate;
      var containerCtrl = ctrls[0];
      var ngModelCtrl = ctrls[1] || $mdUtil.fakeNgModel();
      var formCtrl = ctrls[2];

      if (containerCtrl) {
        var isErrorGetter = containerCtrl.isErrorGetter || function() {
          return ngModelCtrl.$invalid && (ngModelCtrl.$touched || (formCtrl && formCtrl.$submitted));
        };

        containerCtrl.input = element;

        scope.$watch(isErrorGetter, containerCtrl.setInvalid);
      }

      $mdTheming(element);

      // Redirect focus events to the root element, because IE11 is always focusing the container element instead
      // of the md-checkbox element. This causes issues when using ngModelOptions: `updateOnBlur`
      element.children().on('focus', function() {
        element.focus();
      });

      if ($mdUtil.parseAttributeBoolean(attr.mdIndeterminate)) {
        setIndeterminateState();
        scope.$watch(attr.mdIndeterminate, setIndeterminateState);
      }

      if (attr.ngChecked) {
        scope.$watch(scope.$eval.bind(scope, attr.ngChecked), function(value) {
          ngModelCtrl.$setViewValue(value);
          ngModelCtrl.$render();
        });
      }

      $$watchExpr('ngDisabled', 'tabindex', {
        true: '-1',
        false: attr.tabindex
      });

      $mdAria.expectWithText(element, 'aria-label');

      // Reuse the original input[type=checkbox] directive from AngularJS core.
      // This is a bit hacky as we need our own event listener and own render
      // function.
      inputDirective.link.pre(scope, {
        on: angular.noop,
        0: {}
      }, attr, [ngModelCtrl]);

      element.on('click', listener)
        .on('keypress', keypressHandler)
        .on('focus', function() {
          if ($mdInteraction.getLastInteractionType() === 'keyboard') {
            element.addClass('md-focused');
          }
        })
        .on('blur', function() {
          element.removeClass('md-focused');
        });

      ngModelCtrl.$render = render;

      function $$watchExpr(expr, htmlAttr, valueOpts) {
        if (attr[expr]) {
          scope.$watch(attr[expr], function(val) {
            if (valueOpts[val]) {
              element.attr(htmlAttr, valueOpts[val]);
            }
          });
        }
      }

      /**
       * @param {KeyboardEvent} ev 'keypress' event to handle
       */
      function keypressHandler(ev) {
        var keyCode = ev.which || ev.keyCode;
        var submit, form;

        ev.preventDefault();
        switch (keyCode) {
          case $mdConstant.KEY_CODE.SPACE:
            element.addClass('md-focused');
            listener(ev);
            break;
          case $mdConstant.KEY_CODE.ENTER:
            // Match the behavior of the native <input type="checkbox">.
            // When the enter key is pressed while focusing a native checkbox inside a form,
            // the browser will trigger a `click` on the first non-disabled submit button/input
            // in the form. Note that this is different from text inputs, which
            // will directly submit the form without needing a submit button/input to be present.
            form = $mdUtil.getClosest(ev.target, 'form');
            if (form) {
              submit = form.querySelector('button[type="submit"]:enabled, input[type="submit"]:enabled');
              if (submit) {
                submit.click();
              }
            }
            break;
        }
      }

      function listener(ev) {
        // skipToggle boolean is used by the switch directive to prevent the click event
        // when releasing the drag. There will be always a click if releasing the drag over the checkbox
        if (element[0].hasAttribute('disabled') || scope.skipToggle) {
          return;
        }

        scope.$apply(function() {
          // Toggle the checkbox value...
          var viewValue = attr.ngChecked && attr.ngClick ? attr.checked : !ngModelCtrl.$viewValue;

          ngModelCtrl.$setViewValue(viewValue, ev && ev.type);
          ngModelCtrl.$render();
        });
      }

      function render() {
        // Cast the $viewValue to a boolean since it could be undefined
        element.toggleClass('md-checked', !!ngModelCtrl.$viewValue && !isIndeterminate);
      }

      function setIndeterminateState(newValue) {
        isIndeterminate = newValue !== false;
        if (isIndeterminate) {
          element.attr('aria-checked', 'mixed');
        }
        element.toggleClass('md-indeterminate', isIndeterminate);
      }
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.chips
 */
/*
 * @see js folder for chips implementation
 */
angular.module('material.components.chips', [
  'material.core',
  'material.components.autocomplete'
]);

})();
(function(){
"use strict";


MdChipCtrl.$inject = ["$scope", "$element", "$mdConstant", "$timeout", "$mdUtil"];angular
  .module('material.components.chips')
  .controller('MdChipCtrl', MdChipCtrl);

/**
 * Controller for the MdChip component. Responsible for handling keyboard
 * events and editing the chip if needed.
 *
 * @param $scope
 * @param $element
 * @param $mdConstant
 * @param $timeout
 * @param $mdUtil
 * @constructor
 */
function MdChipCtrl ($scope, $element, $mdConstant, $timeout, $mdUtil) {
  /**
   * @type {$scope}
   */
  this.$scope = $scope;

  /**
   * @type {$element}
   */
  this.$element = $element;

  /**
   * @type {$mdConstant}
   */
  this.$mdConstant = $mdConstant;

  /**
   * @type {$timeout}
   */
  this.$timeout = $timeout;

  /**
   * @type {$mdUtil}
   */
  this.$mdUtil = $mdUtil;

  /**
   * @type {boolean}
   */
  this.isEditing = false;

  /**
   * @type {MdChipsCtrl}
   */
  this.parentController = undefined;

  /**
   * @type {boolean}
   */
  this.enableChipEdit = false;
}


/**
 * @param {MdChipsCtrl} controller
 */
MdChipCtrl.prototype.init = function(controller) {
  this.parentController = controller;
  this.enableChipEdit = this.parentController.enableChipEdit;

  if (this.enableChipEdit) {
    this.$element.on('keydown', this.chipKeyDown.bind(this));
    this.$element.on('dblclick', this.chipMouseDoubleClick.bind(this));
    this.getChipContent().addClass('_md-chip-content-edit-is-enabled');
  }
};


/**
 * @return {Object} first element with the md-chip-content class
 */
MdChipCtrl.prototype.getChipContent = function() {
  var chipContents = this.$element[0].getElementsByClassName('md-chip-content');
  return angular.element(chipContents[0]);
};


/**
 * When editing the chip, if the user modifies the existing contents, we'll get a span back and
 * need to ignore text elements as they only contain blank space.
 * `children()` ignores text elements.
 *
 * When editing the chip, if the user deletes the contents and then enters some new content
 * we'll only get a text element back.
 * @return {Object} jQuery object representing the content element of the chip
 */
MdChipCtrl.prototype.getContentElement = function() {
  var contentElement = angular.element(this.getChipContent().children()[0]);
  if (!contentElement || contentElement.length === 0) {
    contentElement = angular.element(this.getChipContent().contents()[0]);
  }
  return contentElement;
};


/**
 * @return {number} index of this chip
 */
MdChipCtrl.prototype.getChipIndex = function() {
  return parseInt(this.$element.attr('index'));
};


/**
 * Update the chip's contents, focus the chip if it's selected, and exit edit mode.
 * If the contents were updated to be empty, remove the chip and re-focus the input element.
 */
MdChipCtrl.prototype.goOutOfEditMode = function() {
  if (!this.isEditing) {
    return;
  }

  this.isEditing = false;
  this.$element.removeClass('_md-chip-editing');
  this.getChipContent()[0].contentEditable = 'false';
  var chipIndex = this.getChipIndex();

  var content = this.getContentElement().text();
  if (content) {
    this.parentController.updateChipContents(chipIndex, content);

    this.$mdUtil.nextTick(function() {
      if (this.parentController.selectedChip === chipIndex) {
        this.parentController.focusChip(chipIndex);
      }
    }.bind(this));
  } else {
    this.parentController.removeChipAndFocusInput(chipIndex);
  }
};


/**
 * Given an HTML element. Selects contents of it.
 * @param {Element} node
 */
MdChipCtrl.prototype.selectNodeContents = function(node) {
  var range, selection;
  if (document.body.createTextRange) {
    range = document.body.createTextRange();
    range.moveToElementText(node);
    range.select();
  } else if (window.getSelection) {
    selection = window.getSelection();
    range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
  }
};


/**
 * Presents an input element to edit the contents of the chip.
 */
MdChipCtrl.prototype.goInEditMode = function() {
  this.isEditing = true;
  this.$element.addClass('_md-chip-editing');
  this.getChipContent()[0].contentEditable = 'true';
  this.getChipContent().on('blur', function() {
    this.goOutOfEditMode();
  }.bind(this));

  this.selectNodeContents(this.getChipContent()[0]);
};


/**
 * Handles the keydown event on the chip element. If enable-chip-edit attribute is
 * set to true, space or enter keys can trigger going into edit mode. Enter can also
 * trigger submitting if the chip is already being edited.
 * @param {KeyboardEvent} event
 */
MdChipCtrl.prototype.chipKeyDown = function(event) {
  if (!this.isEditing &&
    (event.keyCode === this.$mdConstant.KEY_CODE.ENTER ||
      event.keyCode === this.$mdConstant.KEY_CODE.SPACE)) {
    event.preventDefault();
    this.goInEditMode();
  } else if (this.isEditing && event.keyCode === this.$mdConstant.KEY_CODE.ENTER) {
    event.preventDefault();
    this.goOutOfEditMode();
  }
};


/**
 * Enter edit mode if we're not already editing and the enable-chip-edit attribute is enabled.
 */
MdChipCtrl.prototype.chipMouseDoubleClick = function() {
  if (this.enableChipEdit && !this.isEditing) {
    this.goInEditMode();
  }
};

})();
(function(){
"use strict";


MdChip.$inject = ["$mdTheming", "$mdUtil", "$compile", "$timeout"];angular
  .module('material.components.chips')
  .directive('mdChip', MdChip);

/**
 * @ngdoc directive
 * @name mdChip
 * @module material.components.chips
 *
 * @description
 * `<md-chip>` is a component used within `<md-chips>`. It is responsible for rendering an
 * individual chip.
 *
 *
 * @usage
 * <hljs lang="html">
 *   <md-chips>
 *     <md-chip>{{$chip}}</md-chip>
 *   </md-chips>
 * </hljs>
 *
 */

/**
 * MDChip Directive Definition
 *
 * @param $mdTheming
 * @param $mdUtil
 * @param $compile
 * @param $timeout
 * @ngInject
 */
function MdChip($mdTheming, $mdUtil, $compile, $timeout) {
  return {
    restrict: 'E',
    require: ['^?mdChips', 'mdChip'],
    link: postLink,
    controller: 'MdChipCtrl'
  };

  function postLink(scope, element, attr, ctrls) {
    var chipsController = ctrls.shift();
    var chipController = ctrls.shift();
    var chipContentElement = angular.element(element[0].querySelector('.md-chip-content'));

    $mdTheming(element);

    if (chipsController) {
      chipController.init(chipsController);

      // When a chip is blurred, make sure to unset (or reset) the selected chip so that tabbing
      // through elements works properly
      chipContentElement.on('blur', function() {
        chipsController.resetSelectedChip();
        chipsController.$scope.$applyAsync();
      });
    }

    // Use $timeout to ensure we run AFTER the element has been added to the DOM so we can focus it.
    $timeout(function() {
      if (!chipsController) {
        return;
      }

      if (chipsController.shouldFocusLastChip) {
        chipsController.focusLastChipThenInput();
      }
    });
  }
}

})();
(function(){
"use strict";


MdChipRemove.$inject = ["$timeout"];angular
    .module('material.components.chips')
    .directive('mdChipRemove', MdChipRemove);

/**
 * @ngdoc directive
 * @name mdChipRemove
 * @restrict A
 * @module material.components.chips
 *
 * @description
 * Indicates that the associated element should be used as the delete button template for all chips.
 * The associated element must be a child of `md-chips`.
 *
 * The provided button template will be appended to each chip and will remove the associated chip
 * on click.
 *
 * The button is not styled or themed based on the theme set on the `md-chips` component. A theme
 * class and custom icon can be specified in your template.
 *
 * You can also specify the `type` of the button in your template.
 *
 * @usage
 * ### With Standard Chips
 * <hljs lang="html">
 *   <md-chips ...>
 *     <button md-chip-remove class="md-primary" type="button" aria-label="Remove {{$chip}}">
 *       <md-icon md-svg-icon="md-close"></md-icon>
 *     </button>
 *   </md-chips>
 * </hljs>
 *
 * ### With Object Chips
 * <hljs lang="html">
 *   <md-chips ...>
 *     <button md-chip-remove class="md-primary" type="button" aria-label="Remove {{$chip.name}}">
 *       <md-icon md-svg-icon="md-close"></md-icon>
 *     </button>
 *   </md-chips>
 * </hljs>
 */


/**
 * MdChipRemove Directive Definition.
 *
 * @param $timeout
 * @returns {{restrict: string, require: string[], link: Function, scope: boolean}}
 * @constructor
 */
function MdChipRemove ($timeout) {
  return {
    restrict: 'A',
    require: '^mdChips',
    scope: false,
    link: postLink
  };

  function postLink(scope, element, attr, ctrl) {
    element.on('click', function(event) {
      scope.$apply(function() {
        ctrl.removeChip(scope.$$replacedScope.$index);
      });
    });

    // Child elements aren't available until after a $timeout tick as they are hidden by an
    // `ng-if`. see http://goo.gl/zIWfuw
    $timeout(function() {
      element.attr({ 'tabindex': '-1', 'aria-hidden': 'true' });
      element.find('button').attr('tabindex', '-1');
    });
  }
}

})();
(function(){
"use strict";


MdChipTransclude.$inject = ["$compile"];angular
    .module('material.components.chips')
    .directive('mdChipTransclude', MdChipTransclude);

function MdChipTransclude ($compile) {
  return {
    restrict: 'EA',
    terminal: true,
    link: link,
    scope: false
  };
  function link (scope, element, attr) {
    var ctrl = scope.$parent.$mdChipsCtrl,
        newScope = ctrl.parent.$new(false, ctrl.parent);
    newScope.$$replacedScope = scope;
    newScope.$chip = scope.$chip;
    newScope.$index = scope.$index;
    newScope.$mdChipsCtrl = ctrl;

    var newHtml = ctrl.$scope.$eval(attr.mdChipTransclude);

    element.html(newHtml);
    $compile(element.contents())(newScope);
  }
}

})();
(function(){
"use strict";

/**
 * The default chip append delay.
 *
 * @type {number}
 */
MdChipsCtrl.$inject = ["$scope", "$attrs", "$mdConstant", "$log", "$element", "$timeout", "$mdUtil", "$mdLiveAnnouncer", "$exceptionHandler"];
var DEFAULT_CHIP_APPEND_DELAY = 300;

angular
    .module('material.components.chips')
    .controller('MdChipsCtrl', MdChipsCtrl);

/**
 * Controller for the MdChips component. Responsible for adding to and
 * removing from the list of chips, marking chips as selected, and binding to
 * the models of various input components.
 *
 * @param $scope
 * @param $attrs
 * @param $mdConstant
 * @param $log
 * @param $element
 * @param $timeout
 * @param $mdUtil
 * @param $mdLiveAnnouncer
 * @param $exceptionHandler
 * @constructor
 */
function MdChipsCtrl ($scope, $attrs, $mdConstant, $log, $element, $timeout, $mdUtil,
                      $mdLiveAnnouncer, $exceptionHandler) {
  /** @type {Function} **/
  this.$timeout = $timeout;

  /** @type {Object} */
  this.$mdConstant = $mdConstant;

  /** @type {angular.$scope} */
  this.$scope = $scope;

  /** @type {angular.$scope} */
  this.parent = $scope.$parent;

  /** @type {$mdUtil} */
  this.$mdUtil = $mdUtil;

  /** @type {$log} */
  this.$log = $log;

  /** @type {$mdLiveAnnouncer} */
  this.$mdLiveAnnouncer = $mdLiveAnnouncer;

  /** @type {$exceptionHandler} */
  this.$exceptionHandler = $exceptionHandler;

  /** @type {$element} */
  this.$element = $element;

  /** @type {$attrs} */
  this.$attrs = $attrs;

  /** @type {angular.NgModelController} */
  this.ngModelCtrl = null;

  /** @type {angular.NgModelController} */
  this.userInputNgModelCtrl = null;

  /** @type {MdAutocompleteCtrl} */
  this.autocompleteCtrl = null;

  /** @type {Element} */
  this.userInputElement = null;

  /** @type {Array.<Object>} */
  this.items = [];

  /** @type {number} */
  this.selectedChip = -1;

  /** @type {string} */
  this.enableChipEdit = $mdUtil.parseAttributeBoolean($attrs.mdEnableChipEdit);

  /** @type {string} */
  this.addOnBlur = $mdUtil.parseAttributeBoolean($attrs.mdAddOnBlur);

  /**
   * The text to be used as the aria-label for the input.
   * @type {string}
   */
  this.inputAriaLabel = 'Chips input.';

  /**
   * Label text to describe the chips container. Used to give context and instructions to screen
   * reader users when the chips container is selected.
   * @type {string}
   */
  this.containerHint = 'Chips container. Use arrow keys to select chips.';

  /**
   * Label text to describe the chips container when it is empty. Used to give context and
   * instructions to screen reader users when the chips container is selected and it contains
   * no chips.
   * @type {string}
   */
  this.containerEmptyHint =
    'Chips container. Enter the text area, then type text, and press enter to add a chip.';

  /**
   * Hidden hint text for how to delete a chip. Used to give context to screen readers.
   * @type {string}
   */
  this.deleteHint = 'Press delete to remove this chip.';

  /**
   * Hidden label for the delete button. Used to give context to screen readers.
   * @type {string}
   */
  this.deleteButtonLabel = 'Remove';

  /**
   * Model used by the input element.
   * @type {string}
   */
  this.chipBuffer = '';

  /**
   * Whether to use the transformChip expression to transform the chip buffer
   * before appending it to the list.
   * @type {boolean}
   */
  this.useTransformChip = false;

  /**
   * Whether to use the onAdd expression to notify of chip additions.
   * @type {boolean}
   */
  this.useOnAdd = false;

  /**
   * Whether to use the onRemove expression to notify of chip removals.
   * @type {boolean}
   */
  this.useOnRemove = false;

  /**
   * The ID of the chips wrapper which is used to build unique IDs for the chips and the aria-owns
   * attribute.
   *
   * Defaults to '_md-chips-wrapper-' plus a unique number.
   *
   * @type {string}
   */
  this.wrapperId = '';

  /**
   * Array of unique numbers which will be auto-generated any time the items change, and is used to
   * create unique IDs for the aria-owns attribute.
   *
   * @type {Array<number>}
   */
  this.contentIds = [];

  /**
   * The index of the chip that should have it's `tabindex` property set to `0` so it is selectable
   * via the keyboard.
   *
   * @type {number|null}
   */
  this.ariaTabIndex = null;

  /**
   * After appending a chip, the chip will be focused for this number of milliseconds before the
   * input is refocused.
   *
   * **Note:** This is **required** for compatibility with certain screen readers in order for
   * them to properly allow keyboard access.
   *
   * @type {number}
   */
  this.chipAppendDelay = DEFAULT_CHIP_APPEND_DELAY;

  /**
   * Collection of functions to call to un-register watchers
   *
   * @type {Array}
   */
  this.deRegister = [];

  /**
   * The screen reader will announce the chip content followed by this message when a chip is added.
   * @type {string}
   */
  this.addedMessage = 'added';

  /**
   * The screen reader will announce the chip content followed by this message when a chip is
   * removed.
   * @type {string}
   */
  this.removedMessage = 'removed';

  this.init();
}

/**
 * Initializes variables and sets up watchers
 */
MdChipsCtrl.prototype.init = function() {
  var ctrl = this;

  // Set the wrapper ID
  this.wrapperId = '_md-chips-wrapper-' + this.$mdUtil.nextUid();

  // If we're using static chips, then we need to initialize a few things.
  if (!this.$element.attr('ng-model')) {
    this.setupStaticChips();
  }

  // Setup a watcher which manages the role and aria-owns attributes.
  // This is never called for static chips since items is not defined.
  this.deRegister.push(
    this.$scope.$watchCollection('$mdChipsCtrl.items', function() {
      // Make sure our input and wrapper have the correct ARIA attributes
      ctrl.setupInputAria();
      ctrl.setupWrapperAria();
    })
  );

  this.deRegister.push(
    this.$attrs.$observe('mdChipAppendDelay', function(newValue) {
      ctrl.chipAppendDelay = parseInt(newValue) || DEFAULT_CHIP_APPEND_DELAY;
    })
  );
};

/**
 * Destructor for cleanup
 */
MdChipsCtrl.prototype.$onDestroy = function $onDestroy() {
  var $destroyFn;
  while (($destroyFn = this.deRegister.pop())) {
    $destroyFn.call(this);
  }
};

/**
 * If we have an input, ensure it has the appropriate ARIA attributes.
 */
MdChipsCtrl.prototype.setupInputAria = function() {
  var input = this.$element.find('input');

  // If we have no input, just return
  if (!input) {
    return;
  }

  input.attr('role', 'textbox');
  input.attr('aria-multiline', true);
  if (this.inputAriaDescribedBy) {
    input.attr('aria-describedby', this.inputAriaDescribedBy);
  }
  if (this.inputAriaLabelledBy) {
    input.attr('aria-labelledby', this.inputAriaLabelledBy);
    input.removeAttr('aria-label');
  } else {
    input.attr('aria-label', this.inputAriaLabel);
  }
};

/**
 * Ensure our wrapper has the appropriate ARIA attributes.
 */
MdChipsCtrl.prototype.setupWrapperAria = function() {
  var ctrl = this,
      wrapper = this.$element.find('md-chips-wrap');

  if (this.items && this.items.length) {
    // Dynamically add the listbox role on every change because it must be removed when there are
    // no items.
    wrapper.attr('role', 'listbox');

    // Generate some random (but unique) IDs for each chip
    this.contentIds = this.items.map(function() {
      return ctrl.wrapperId + '-chip-' + ctrl.$mdUtil.nextUid();
    });

    // Use the contentIDs above to generate the aria-owns attribute
    wrapper.attr('aria-owns', this.contentIds.join(' '));
    wrapper.attr('aria-label', this.containerHint);
  } else {
    // If we have no items, then the role and aria-owns attributes MUST be removed
    wrapper.removeAttr('role');
    wrapper.removeAttr('aria-owns');
    wrapper.attr('aria-label', this.containerEmptyHint);
  }
};

/**
 * Apply specific roles and aria attributes for static chips
 */
MdChipsCtrl.prototype.setupStaticChips = function() {
  var ctrl = this, i, staticChips;
  var wrapper = this.$element.find('md-chips-wrap');

  this.$timeout(function() {
    wrapper.attr('role', 'list');
    staticChips = wrapper[0].children;
    for (i = 0; i < staticChips.length; i++) {
      staticChips[i].setAttribute('role', 'listitem');
      staticChips[i].setAttribute('aria-setsize', staticChips.length);
    }
    if (ctrl.inputAriaDescribedBy) {
      wrapper.attr('aria-describedby', ctrl.inputAriaDescribedBy);
    }
    if (ctrl.inputAriaLabelledBy) {
      wrapper.attr('aria-labelledby', ctrl.inputAriaLabelledBy);
      wrapper.removeAttr('aria-label');
    } else {
      wrapper.attr('aria-label', ctrl.inputAriaLabel);
    }
  }, 10);
};

/**
 * Handles the keydown event on the input element: by default <enter> appends
 * the buffer to the chip list, while backspace removes the last chip in the
 * list if the current buffer is empty.
 * @param {jQuery.Event|KeyboardEvent} event
 */
MdChipsCtrl.prototype.inputKeydown = function(event) {
  var chipBuffer = this.getChipBuffer();

  // If we have an autocomplete, and it handled the event, we have nothing to do
  if (this.autocompleteCtrl && event.isDefaultPrevented && event.isDefaultPrevented()) {
    return;
  }

  if (event.keyCode === this.$mdConstant.KEY_CODE.BACKSPACE) {
    // Only select and focus the previous chip, if the current caret position of the
    // input element is at the beginning.
    if (this.getCursorPosition(event.target) !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.items.length) {
      this.selectAndFocusChipSafe(this.items.length - 1);
    }

    return;
  }

  // By default <enter> appends the buffer to the chip list.
  if (!this.separatorKeys || this.separatorKeys.length < 1) {
    this.separatorKeys = [this.$mdConstant.KEY_CODE.ENTER];
  }

  // Support additional separator key codes in an array of `md-separator-keys`.
  if (this.separatorKeys.indexOf(event.keyCode) !== -1) {
    if ((this.autocompleteCtrl && this.requireMatch) || !chipBuffer) return;
    event.preventDefault();

    // Only append the chip and reset the chip buffer if the max chips limit isn't reached.
    if (this.hasMaxChipsReached()) return;

    this.appendChip(chipBuffer.trim());
    this.resetChipBuffer();

    return false;
  }
};

/**
 * Returns the cursor position of the specified input element.
 * @param {HTMLInputElement} element relevant input element
 * @returns {Number} Cursor Position of the input.
 */
MdChipsCtrl.prototype.getCursorPosition = function(element) {
  /*
   * Figure out whether the current input for the chips buffer is valid for using
   * the selectionStart / end property to retrieve the cursor position.
   * Some browsers do not allow the use of those attributes, on different input types.
   */
  try {
    if (element.selectionStart === element.selectionEnd) {
      return element.selectionStart;
    }
  } catch (e) {
    if (!element.value) {
      return 0;
    }
  }
};


/**
 * Updates the content of the chip at given index
 * @param {number} chipIndex
 * @param {string} chipContents
 */
MdChipsCtrl.prototype.updateChipContents = function(chipIndex, chipContents) {
  if (chipIndex >= 0 && chipIndex < this.items.length) {
    this.items[chipIndex] = chipContents;
    this.updateNgModel(true);
  }
};


/**
 * @return {boolean} true if a chip is currently being edited. False otherwise.
 */
MdChipsCtrl.prototype.isEditingChip = function() {
  return !!this.$element[0].querySelector('._md-chip-editing');
};

/**
 * @param {string|Object} chip contents of a single chip
 * @returns {boolean} true if the chip is an Object, false otherwise.
 * @private
 */
MdChipsCtrl.prototype._isChipObject = function(chip) {
  return angular.isObject(chip);
};

/**
 * @returns {boolean} true if chips can be removed, false otherwise.
 */
MdChipsCtrl.prototype.isRemovable = function() {
  // Return false if we have static chips
  if (!this.ngModelCtrl) {
    return false;
  }

  return this.readonly ? this.removable :
         angular.isDefined(this.removable) ? this.removable : true;
};

/**
 * Handles the keydown event on the chip elements: backspace removes the selected chip, arrow
 * keys switch which chip is active.
 * @param {KeyboardEvent} event
 */
MdChipsCtrl.prototype.chipKeydown = function (event) {
  if (this.getChipBuffer()) return;
  if (this.isEditingChip()) return;

  switch (event.keyCode) {
    case this.$mdConstant.KEY_CODE.BACKSPACE:
    case this.$mdConstant.KEY_CODE.DELETE:
      if (this.selectedChip < 0) return;
      event.preventDefault();
      // Cancel the delete action only after the event cancel. Otherwise the page will go back.
      if (!this.isRemovable()) return;
      this.removeAndSelectAdjacentChip(this.selectedChip, event);
      break;
    case this.$mdConstant.KEY_CODE.LEFT_ARROW:
      event.preventDefault();
      // By default, allow selection of -1 which will focus the input; if we're readonly, don't go
      // below 0.
      if (this.selectedChip < 0 || (this.readonly && this.selectedChip === 0)) {
        this.selectedChip = this.items.length;
      }
      if (this.items.length) this.selectAndFocusChipSafe(this.selectedChip - 1);
      break;
    case this.$mdConstant.KEY_CODE.RIGHT_ARROW:
      event.preventDefault();
      this.selectAndFocusChipSafe(this.selectedChip + 1);
      break;
    case this.$mdConstant.KEY_CODE.ESCAPE:
    case this.$mdConstant.KEY_CODE.TAB:
      if (this.selectedChip < 0) return;
      event.preventDefault();
      this.onFocus();
      break;
  }
};

/**
 * Get the input's placeholder - uses `placeholder` when list is empty and `secondary-placeholder`
 * when the list is non-empty. If `secondary-placeholder` is not provided, `placeholder` is used
 * always.
 * @returns {string}
 */
MdChipsCtrl.prototype.getPlaceholder = function() {
  // Allow `secondary-placeholder` to be blank.
  var useSecondary = (this.items && this.items.length &&
      (this.secondaryPlaceholder === '' || this.secondaryPlaceholder));
  return useSecondary ? this.secondaryPlaceholder : this.placeholder;
};

/**
 * Removes chip at {@code index} and selects the adjacent chip.
 * @param {number} index adjacent chip to select
 * @param {Event=} event
 */
MdChipsCtrl.prototype.removeAndSelectAdjacentChip = function(index, event) {
  var self = this;
  var selIndex = self.getAdjacentChipIndex(index);
  var wrap = this.$element[0].querySelector('md-chips-wrap');
  var chip = this.$element[0].querySelector('md-chip[index="' + index + '"]');

  self.removeChip(index, event);

  // The double-timeout is currently necessary to ensure that the DOM has finalized and the select()
  // will find the proper chip since the selection is index-based.
  //
  // TODO: Investigate calling from within chip $scope.$on('$destroy') to reduce/remove timeouts
  self.$timeout(function() {
    self.$timeout(function() {
      self.selectAndFocusChipSafe(selIndex);
    });
  });
};

/**
 * Sets the selected chip index to -1.
 */
MdChipsCtrl.prototype.resetSelectedChip = function() {
  this.selectedChip = -1;
  this.ariaTabIndex = null;
};

/**
 * Gets the index of an adjacent chip to select after deletion. Adjacency is
 * determined as the next chip in the list, unless the target chip is the
 * last in the list, then it is the chip immediately preceding the target. If
 * there is only one item in the list, -1 is returned (select none).
 * The number returned is the index to select AFTER the target has been removed.
 * If the current chip is not selected, then -1 is returned to select none.
 * @param {number} index
 * @returns {number}
 */
MdChipsCtrl.prototype.getAdjacentChipIndex = function(index) {
  var len = this.items.length - 1;
  return (len === 0) ? -1 :
      (index === len) ? index - 1 : index;
};

/**
 * Append the contents of the buffer to the chip list. This method will first
 * call out to the md-transform-chip method, if provided.
 * @param {string} newChip chip buffer contents that will be used to create the new chip
 */
MdChipsCtrl.prototype.appendChip = function(newChip) {
  this.shouldFocusLastChip = !this.addOnBlur;
  if (this.useTransformChip && this.transformChip) {
    var transformedChip = this.transformChip({'$chip': newChip});

    // Check to make sure the chip is defined before assigning it, otherwise, we'll just assume
    // they want the string version.
    if (angular.isDefined(transformedChip)) {
      newChip = transformedChip;
    }
  }

  // If items contains an identical object to newChip, do not append
  if (angular.isObject(newChip)) {
    var identical = this.items.some(function(item) {
      return angular.equals(newChip, item);
    });
    if (identical) return;
  }

  // Check for a null (but not undefined), or existing chip and cancel appending
  if (newChip == null || this.items.indexOf(newChip) + 1) return;

  // Append the new chip onto our list
  var length = this.items.push(newChip);
  var index = length - 1;

  this.updateNgModel();

  // Tell screen reader users that the chip was successfully added.
  // TODO add a way for developers to specify which field of the object should be announced here.
  var chipContent = angular.isObject(newChip) ? '' : newChip;
  this.$mdLiveAnnouncer.announce(chipContent + ' ' + this.addedMessage, 'assertive');

  // If the md-on-add attribute is specified, send a chip addition event
  if (this.useOnAdd && this.onAdd) {
    this.onAdd({ '$chip': newChip, '$index': index });
  }
};

/**
 * Sets whether to use the md-transform-chip expression. This expression is
 * bound to scope and controller in {@code MdChipsDirective} as
 * {@code transformChip}. Due to the nature of directive scope bindings, the
 * controller cannot know on its own/from the scope whether an expression was
 * actually provided.
 */
MdChipsCtrl.prototype.useTransformChipExpression = function() {
  this.useTransformChip = true;
};

/**
 * Sets whether to use the md-on-add expression. This expression is
 * bound to scope and controller in {@code MdChipsDirective} as
 * {@code onAdd}. Due to the nature of directive scope bindings, the
 * controller cannot know on its own/from the scope whether an expression was
 * actually provided.
 */
MdChipsCtrl.prototype.useOnAddExpression = function() {
  this.useOnAdd = true;
};

/**
 * Sets whether to use the md-on-remove expression. This expression is
 * bound to scope and controller in {@code MdChipsDirective} as
 * {@code onRemove}. Due to the nature of directive scope bindings, the
 * controller cannot know on its own/from the scope whether an expression was
 * actually provided.
 */
MdChipsCtrl.prototype.useOnRemoveExpression = function() {
  this.useOnRemove = true;
};

/**
 * Sets whether to use the md-on-select expression. This expression is
 * bound to scope and controller in {@code MdChipsDirective} as
 * {@code onSelect}. Due to the nature of directive scope bindings, the
 * controller cannot know on its own/from the scope whether an expression was
 * actually provided.
 */
MdChipsCtrl.prototype.useOnSelectExpression = function() {
  this.useOnSelect = true;
};

/**
 * Gets the input buffer. The input buffer can be the model bound to the
 * default input item {@code this.chipBuffer}, the {@code selectedItem}
 * model of an {@code md-autocomplete}, or, through some magic, the model
 * bound to any input or text area element found within a
 * {@code md-input-container} element.
 * @return {string} the input buffer
 */
MdChipsCtrl.prototype.getChipBuffer = function() {
  var chipBuffer =  !this.userInputElement ? this.chipBuffer :
                     this.userInputNgModelCtrl ? this.userInputNgModelCtrl.$viewValue :
                     this.userInputElement[0].value;

  // Ensure that the chip buffer is always a string. For example, the input element buffer
  // might be falsy.
  return angular.isString(chipBuffer) ? chipBuffer : '';
};

/**
 * Resets the input buffer for either the internal input or user provided input element.
 */
MdChipsCtrl.prototype.resetChipBuffer = function() {
  if (this.userInputElement) {
    if (this.userInputNgModelCtrl) {
      this.userInputNgModelCtrl.$setViewValue('');
      this.userInputNgModelCtrl.$render();
    } else {
      this.userInputElement[0].value = '';
    }
  } else {
    this.chipBuffer = '';
  }
};

/**
 * @returns {boolean} true if the max chips limit has been reached, false otherwise.
 */
MdChipsCtrl.prototype.hasMaxChipsReached = function() {
  if (angular.isString(this.maxChips)) this.maxChips = parseInt(this.maxChips, 10) || 0;

  return this.maxChips > 0 && this.items.length >= this.maxChips;
};

/**
 * Updates the validity properties for the ngModel.
 *
 * TODO add the md-max-chips validator to this.ngModelCtrl.validators so that the validation will
 * be performed automatically.
 */
MdChipsCtrl.prototype.validateModel = function() {
  this.ngModelCtrl.$setValidity('md-max-chips', !this.hasMaxChipsReached());
  this.ngModelCtrl.$validate(); // rerun any registered validators
};

/**
 * Function to handle updating the model, validation, and change notification when a chip
 * is added, removed, or changed.
 * @param {boolean=} skipValidation true to skip calling validateModel()
 */
MdChipsCtrl.prototype.updateNgModel = function(skipValidation) {
  if (!skipValidation) {
    this.validateModel();
  }
  // This will trigger ng-change to fire, even in cases where $setViewValue() would not.
  angular.forEach(this.ngModelCtrl.$viewChangeListeners, function(listener) {
    try {
      listener();
    } catch (e) {
      this.$exceptionHandler(e);
    }
  });
};

/**
 * Removes the chip at the given index.
 * @param {number} index of chip to remove
 * @param {Event=} event optionally passed to the onRemove callback
 */
MdChipsCtrl.prototype.removeChip = function(index, event) {
  var removed = this.items.splice(index, 1);

  this.updateNgModel();
  this.ngModelCtrl.$setDirty();

  // Tell screen reader users that the chip was successfully removed.
  // TODO add a way for developers to specify which field of the object should be announced here.
  var chipContent = angular.isObject(removed[0]) ? '' : removed[0];
  this.$mdLiveAnnouncer.announce(chipContent + ' ' + this.removedMessage, 'assertive');

  if (removed && removed.length && this.useOnRemove && this.onRemove) {
    this.onRemove({ '$chip': removed[0], '$index': index, '$event': event });
  }
};

/**
 * @param {number} index location of chip to remove
 * @param {Event=} $event
 */
MdChipsCtrl.prototype.removeChipAndFocusInput = function (index, $event) {
  this.removeChip(index, $event);

  if (this.autocompleteCtrl) {
    // Always hide the autocomplete dropdown before focusing the autocomplete input.
    // Wait for the input to move horizontally, because the chip was removed.
    // This can lead to an incorrect dropdown position.
    this.autocompleteCtrl.hidden = true;
    this.$mdUtil.nextTick(this.onFocus.bind(this));
  } else {
    this.onFocus();
  }

};
/**
 * Selects the chip at `index`,
 * @param {number} index location of chip to select and focus
 */
MdChipsCtrl.prototype.selectAndFocusChipSafe = function(index) {
  // If we have no chips, or are asked to select a chip before the first, just focus the input
  if (!this.items.length || index === -1) {
    return this.focusInput();
  }

  // If we are asked to select a chip greater than the number of chips...
  if (index >= this.items.length) {
    if (this.readonly) {
      // If we are readonly, jump back to the start (because we have no input)
      index = 0;
    } else {
      // If we are not readonly, we should attempt to focus the input
      return this.onFocus();
    }
  }

  index = Math.max(index, 0);
  index = Math.min(index, this.items.length - 1);

  this.selectChip(index);
  this.focusChip(index);
};

/**
 * Focus last chip, then focus the input. This is needed for screen reader support.
 */
MdChipsCtrl.prototype.focusLastChipThenInput = function() {
  var ctrl = this;

  ctrl.shouldFocusLastChip = false;

  ctrl.focusChip(this.items.length - 1);

  ctrl.$timeout(function() {
    ctrl.focusInput();
  }, ctrl.chipAppendDelay);
};

/**
 * Focus the input element.
 */
MdChipsCtrl.prototype.focusInput = function() {
  this.selectChip(-1);
  this.onFocus();
};

/**
 * Marks the chip at the given index as selected.
 * @param {number} index location of chip to select
 */
MdChipsCtrl.prototype.selectChip = function(index) {
  if (index >= -1 && index <= this.items.length) {
    this.selectedChip = index;

    // Fire the onSelect if provided
    if (this.useOnSelect && this.onSelect) {
      this.onSelect({'$chip': this.items[index] });
    }
  } else {
    this.$log.warn('Selected Chip index out of bounds; ignoring.');
  }
};

/**
 * Selects the chip at {@code index} and gives it focus.
 * @param {number} index location of chip to select and focus
 * @deprecated use MdChipsCtrl.selectAndFocusChipSafe. Will be removed in 1.2.
 */
MdChipsCtrl.prototype.selectAndFocusChip = function(index) {
  this.selectChip(index);
  if (index !== -1) {
    this.focusChip(index);
  }
};

/**
 * Call {@code focus()} on the chip at {@code index}
 * @param {number} index location of chip to focus
 */
MdChipsCtrl.prototype.focusChip = function(index) {
  var chipContent = this.$element[0].querySelector(
    'md-chip[index="' + index + '"] .md-chip-content'
  );

  this.ariaTabIndex = index;

  chipContent.focus();
};

/**
 * Configures the required interactions with the ngModel Controller.
 * Specifically, set {@code this.items} to the {@code NgModelController#$viewValue}.
 * @param {NgModelController} ngModelCtrl
 */
MdChipsCtrl.prototype.configureNgModel = function(ngModelCtrl) {
  this.ngModelCtrl = ngModelCtrl;

  var self = this;

  // in chips the meaning of $isEmpty changes
  ngModelCtrl.$isEmpty = function(value) {
    return !value || value.length === 0;
  };

  ngModelCtrl.$render = function() {
    // model is updated. do something.
    self.items = self.ngModelCtrl.$viewValue;
  };
};

MdChipsCtrl.prototype.onFocus = function () {
  var input = this.$element[0].querySelector('input');
  input && input.focus();
  this.resetSelectedChip();
};

MdChipsCtrl.prototype.onInputFocus = function () {
  this.inputHasFocus = true;

  // Make sure we have the appropriate ARIA attributes
  this.setupInputAria();

  // Make sure we don't have any chips selected
  this.resetSelectedChip();
};

MdChipsCtrl.prototype.onInputBlur = function () {
  this.inputHasFocus = false;

  if (this.shouldAddOnBlur()) {
    this.appendChip(this.getChipBuffer().trim());
    this.resetChipBuffer();
  }
};

/**
 * Configure event bindings on input element.
 * @param {angular.element} inputElement
 */
MdChipsCtrl.prototype.configureInput = function configureInput(inputElement) {
  // Find the NgModelCtrl for the input element
  var ngModelCtrl = inputElement.controller('ngModel');
  var ctrl = this;

  if (ngModelCtrl) {

    // sync touched-state from inner input to chips-element
    this.deRegister.push(
      this.$scope.$watch(
        function() {
          return ngModelCtrl.$touched;
        },
        function(isTouched) {
          isTouched && ctrl.ngModelCtrl.$setTouched();
        }
      )
    );

    // sync dirty-state from inner input to chips-element
    this.deRegister.push(
      this.$scope.$watch(
        function() {
          return ngModelCtrl.$dirty;
        },
        function(isDirty) {
          isDirty && ctrl.ngModelCtrl.$setDirty();
        }
      )
    );
  }
};

/**
 * Configure event bindings on a user-provided input element.
 * @param {angular.element} inputElement
 */
MdChipsCtrl.prototype.configureUserInput = function(inputElement) {
  this.userInputElement = inputElement;

  // Find the NgModelCtrl for the input element
  var ngModelCtrl = inputElement.controller('ngModel');
  // `.controller` will look in the parent as well.
  if (ngModelCtrl !== this.ngModelCtrl) {
    this.userInputNgModelCtrl = ngModelCtrl;
  }

  var scope = this.$scope;
  var ctrl = this;

  // Run all of the events using evalAsync because a focus may fire a blur in the same digest loop
  var scopeApplyFn = function(event, fn) {
    scope.$evalAsync(angular.bind(ctrl, fn, event));
  };

  // Bind to keydown and focus events of input
  inputElement
      .attr({ tabindex: 0 })
      .on('keydown', function(event) { scopeApplyFn(event, ctrl.inputKeydown); })
      .on('focus', function(event) { scopeApplyFn(event, ctrl.onInputFocus); })
      .on('blur', function(event) { scopeApplyFn(event, ctrl.onInputBlur); });
};

/**
 * @param {MdAutocompleteCtrl} ctrl controller from the autocomplete component
 */
MdChipsCtrl.prototype.configureAutocomplete = function(ctrl) {
  if (ctrl) {
    this.autocompleteCtrl = ctrl;
    // Update the default container empty hint when we're inside of an autocomplete.
    if (!this.$element.attr('container-empty-hint')) {
      this.containerEmptyHint = 'Chips container with autocompletion. Enter the text area, ' +
        'type text to search, and then use the up and down arrow keys to select an option. ' +
        'Press enter to add the selected option as a chip.';
      this.setupWrapperAria();
    }

    ctrl.registerSelectedItemWatcher(angular.bind(this, function (item) {
      if (item) {
        // Only append the chip and reset the chip buffer if the max chips limit isn't reached.
        if (this.hasMaxChipsReached()) return;

        this.appendChip(item);
        this.resetChipBuffer();
      }
    }));

    this.$element.find('input')
        .on('focus',angular.bind(this, this.onInputFocus))
        .on('blur', angular.bind(this, this.onInputBlur));
  }
};

/**
 * @returns {boolean} Whether the current chip buffer should be added on input blur or not.
 */
MdChipsCtrl.prototype.shouldAddOnBlur = function() {

  // Update the custom ngModel validators from the chips component.
  this.validateModel();

  var chipBuffer = this.getChipBuffer().trim();
  // If the model value is empty and required is set on the element, then the model will be invalid.
  // In that case, we still want to allow adding the chip. The main (but not only) case we want
  // to disallow is adding a chip on blur when md-max-chips validation fails.
  var isModelValid = this.ngModelCtrl.$isEmpty(this.ngModelCtrl.$modelValue) ||
    this.ngModelCtrl.$valid;
  var isAutocompleteShowing = this.autocompleteCtrl && !this.autocompleteCtrl.hidden;

  if (this.userInputNgModelCtrl) {
    isModelValid = isModelValid && this.userInputNgModelCtrl.$valid;
  }

  return this.addOnBlur && !this.requireMatch && chipBuffer && isModelValid &&
    !isAutocompleteShowing;
};

/**
 * @returns {boolean} true if the input or a chip is focused. False otherwise.
 */
MdChipsCtrl.prototype.hasFocus = function () {
  return this.inputHasFocus || this.selectedChip >= 0;
};

/**
 * @param {number} index location of content id
 * @returns {number} unique id for the aria-owns attribute
 */
MdChipsCtrl.prototype.contentIdFor = function(index) {
  return this.contentIds[index];
};

})();
(function(){
"use strict";

  
  MdChips.$inject = ["$mdTheming", "$mdUtil", "$compile", "$log", "$timeout", "$$mdSvgRegistry"];angular
      .module('material.components.chips')
      .directive('mdChips', MdChips);

  /**
   * @ngdoc directive
   * @name mdChips
   * @module material.components.chips
   *
   * @description
   * `<md-chips>` is an input component for building lists of strings or objects. The list items are
   * displayed as 'chips'. This component can make use of an `<input>` element or an
   * `<md-autocomplete>` element.
   *
   * ### Custom templates
   * A custom template may be provided to render the content of each chip. This is achieved by
   * specifying an `<md-chip-template>` element containing the custom content as a child of
   * `<md-chips>`.
   *
   * Note: Any attributes on
   * `<md-chip-template>` will be dropped as only the innerHTML is used for the chip template. The
   * variables `$chip` and `$index` are available in the scope of `<md-chip-template>`, representing
   * the chip object and its index in the list of chips, respectively.
   * To override the chip delete control, include an element (ideally a button) with the attribute
   * `md-chip-remove`. A click listener to remove the chip will be added automatically. The element
   * is also placed as a sibling to the chip content (on which there are also click listeners) to
   * avoid a nested ng-click situation.
   *
   * <!-- Note: We no longer want to include this in the site docs; but it should remain here for
   * future developers and those looking at the documentation.
   *
   * <h3> Pending Features </h3>
   * <ul style="padding-left:20px;">
   *
   *   <ul>Style
   *     <li>Colours for hover, press states (ripple?).</li>
   *   </ul>
   *
   *   <ul>Validation
   *     <li>allow a validation callback</li>
   *     <li>highlighting style for invalid chips</li>
   *   </ul>
   *
   *   <ul>Item mutation
   *     <li>Support `
   *       <md-chip-edit>` template, show/hide the edit element on tap/click? double tap/double
   *       click?
   *     </li>
   *   </ul>
   *
   *   <ul>Truncation and Disambiguation (?)
   *     <li>Truncate chip text where possible, but do not truncate entries such that two are
   *     indistinguishable.</li>
   *   </ul>
   *
   *   <ul>Drag and Drop
   *     <li>Drag and drop chips between related `<md-chips>` elements.
   *     </li>
   *   </ul>
   * </ul>
   *
   * //-->
   *
   * Sometimes developers want to limit the amount of possible chips.<br/>
   * You can specify the maximum amount of chips by using the following markup.
   *
   * <hljs lang="html">
   *   <md-chips
   *       ng-model="myItems"
   *       placeholder="Add an item"
   *       md-max-chips="5">
   *   </md-chips>
   * </hljs>
   *
   * In some cases, you have an autocomplete inside of the `md-chips`.<br/>
   * When the maximum amount of chips has been reached, you can also disable the autocomplete
   * selection.<br/>
   * Here is an example markup.
   *
   * <hljs lang="html">
   *   <md-chips ng-model="myItems" md-max-chips="5">
   *     <md-autocomplete ng-hide="myItems.length > 5" ...></md-autocomplete>
   *   </md-chips>
   * </hljs>
   *
   * ### Accessibility
   *
   * The `md-chips` component supports keyboard and screen reader users since Version 1.1.2. In
   * order to achieve this, we modified the chips behavior to select newly appended chips for
   * `300ms` before re-focusing the input and allowing the user to type.
   *
   * For most users, this delay is small enough that it will not be noticeable but allows certain
   * screen readers to function properly (JAWS and NVDA in particular).
   *
   * We introduced a new `md-chip-append-delay` option to allow developers to better control this
   * behavior.
   *
   * Please refer to the documentation of this option (below) for more information.
   *
   * @param {expression} ng-model Assignable AngularJS expression to be data-bound to the list of
   *    chips. The expression should evaluate to a `string` or `Object` Array. The type of this
   *    array should align with the return value of `md-transform-chip`.
   * @param {expression=} ng-change AngularJS expression to be executed on chip addition, removal,
   *    or content change.
   * @param {string=} placeholder Placeholder text that will be forwarded to the input.
   * @param {string=} secondary-placeholder Placeholder text that will be forwarded to the input,
   *    displayed when there is at least one item in the list
   * @param {boolean=} md-removable Enables or disables the deletion of chips through the
   *    removal icon or the Delete/Backspace key. Defaults to true.
   * @param {boolean=} readonly Disables list manipulation (deleting or adding list items), hiding
   *    the input and delete buttons. If no `ng-model` is provided, the chips will automatically be
   *    marked as readonly.<br/><br/>
   *    When `md-removable` is not defined, the `md-remove` behavior will be overwritten and
   *    disabled.
   * @param {boolean=} md-enable-chip-edit Set this to `"true"` to enable editing of chip contents.
   *    The user can go into edit mode by pressing the `space` or `enter` keys, or by double
   *    clicking on the chip. Chip editing is only supported for chips using the basic template.
   *    **Note:** This attribute is only evaluated once; it is not watched.
   * @param {boolean=} ng-required Whether ng-model is allowed to be empty or not.
   * @param {number=} md-max-chips The maximum number of chips allowed to add through user input.
   *    <br/><br/>The validation property `md-max-chips` can be used when the max chips
   *    amount is reached.
   * @param {boolean=} md-add-on-blur When set to `"true"`, the remaining text inside of the input
   *    will be converted into a new chip on blur.
   *    **Note:** This attribute is only evaluated once; it is not watched.
   * @param {expression} md-transform-chip An expression of form `myFunction($chip)` that when
   *    called expects one of the following return values:
   *    - an object representing the `$chip` input string
   *    - `undefined` to simply add the `$chip` input string, or
   *    - `null` to prevent the chip from being appended
   * @param {expression=} md-on-add An expression which will be called when a chip has been
   *    added with `$chip` and `$index` available as parameters.
   * @param {expression=} md-on-remove An expression which will be called when a chip has been
   *    removed with `$chip`, `$index`, and `$event` available as parameters.
   * @param {expression=} md-on-select An expression which will be called when a chip is selected.
   * @param {boolean=} md-require-match If true, and the chips template contains an autocomplete,
   *    only allow selection of pre-defined chips (i.e. you cannot add new ones).
   * @param {string=} input-aria-describedby A space-separated list of element IDs. This should
   *     contain the IDs of any elements that describe this autocomplete. Screen readers will read
   *     the content of these elements at the end of announcing that the chips input has been
   *     selected and describing its current state. The descriptive elements do not need to be
   *     visible on the page.
   * @param {string=} input-aria-labelledby A space-separated list of element IDs. The ideal use
   *    case is that this would contain the ID of a `<label>` element that is associated with these
   *    chips.<br><br>
   *    For `<label id="state">US State</label>`, you would set this to
   *    `input-aria-labelledby="state"`.
   * @param {string=} input-aria-label A string read by screen readers to identify the input.
   *    For static chips, this will be applied to the chips container.
   * @param {string=} container-hint A string read by screen readers informing users of how to
   *    navigate the chips when there are chips. Only applies when `ng-model` is defined.
   * @param {string=} container-empty-hint A string read by screen readers informing users of how to
   *    add chips when there are no chips. You will want to use this to override the default when
   *    in a non-English locale. Only applies when `ng-model` is defined.
   * @param {string=} delete-hint A string read by screen readers instructing users that pressing
   *    the delete key will remove the chip. You will want to use this to override the default when
   *    in a non-English locale.
   * @param {string=} delete-button-label <strong>Deprecated</strong> A label for the delete button.
   *    Used to be read by screen readers.
   * @param {string=} md-removed-message Screen readers will announce this message following the
   *    chips contents. The default is `"removed"`. If a chip with the content of "Apple" was
   *    removed, the screen reader would read "Apple removed". You will want to use this to override
   *    the default when in a non-English locale.
   * @param {string=} md-added-message Screen readers will announce this message following the
   *    chips contents. The default is `"added"`. If a chip with the content of "Apple" was
   *    created, the screen reader would read "Apple added". You will want to use this to override
   *    the default when in a non-English locale.
   * @param {expression=} md-separator-keys An array of key codes used to separate chips.
   * @param {string=} md-chip-append-delay The number of milliseconds that the component will select
   *    a newly appended chip before allowing a user to type into the input. This is **necessary**
   *    for keyboard accessibility for screen readers. It defaults to 300ms and any number less than
   *    300 can cause issues with screen readers (particularly JAWS and sometimes NVDA).
   *
   *    _Available since Version 1.1.2._
   *
   *    **Note:** You can safely set this to `0` in one of the following two instances:
   *
   *    1. You are targeting an iOS or Safari-only application (where users would use VoiceOver) or
   *    only ChromeVox users.
   *
   *    2. If you have utilized the `md-separator-keys` to disable the `enter` keystroke in
   *    favor of another one (such as `,` or `;`).
   *
   * @usage
   * <hljs lang="html">
   *   <md-chips
   *       ng-model="myItems"
   *       placeholder="Add an item"
   *       readonly="isReadOnly">
   *   </md-chips>
   * </hljs>
   *
   * <h3>Validation</h3>
   * When using [ngMessages](https://docs.angularjs.org/api/ngMessages), you can show errors based
   * on our custom validators.
   * <hljs lang="html">
   *   <form name="userForm">
   *     <md-chips
   *       name="fruits"
   *       ng-model="myItems"
   *       placeholder="Add an item"
   *       md-max-chips="5">
   *     </md-chips>
   *     <div ng-messages="userForm.fruits.$error" ng-if="userForm.$dirty">
   *       <div ng-message="md-max-chips">You reached the maximum amount of chips</div>
   *    </div>
   *   </form>
   * </hljs>
   *
   */

  // TODO add a way for developers to specify which field of the object should used in the
  // aria-label.
  var MD_CHIPS_TEMPLATE = '\
      <md-chips-wrap\
          id="{{$mdChipsCtrl.wrapperId}}"\
          tabindex="{{$mdChipsCtrl.readonly ? 0 : -1}}"\
          ng-keydown="$mdChipsCtrl.chipKeydown($event)"\
          ng-class="{ \'md-focused\': $mdChipsCtrl.hasFocus(), \
                      \'md-readonly\': !$mdChipsCtrl.ngModelCtrl || $mdChipsCtrl.readonly,\
                      \'md-removable\': $mdChipsCtrl.isRemovable() }"\
          class="md-chips">\
        <md-chip ng-repeat="$chip in $mdChipsCtrl.items"\
            index="{{$index}}" \
            ng-class="{\'md-focused\': $mdChipsCtrl.selectedChip == $index, \'md-readonly\': !$mdChipsCtrl.ngModelCtrl || $mdChipsCtrl.readonly}">\
          <div class="md-chip-content"\
              tabindex="{{$mdChipsCtrl.ariaTabIndex === $index ? 0 : -1}}"\
              id="{{$mdChipsCtrl.contentIdFor($index)}}"\
              role="option"\
              aria-selected="{{$mdChipsCtrl.selectedChip === $index}}"\
              aria-setsize="{{$mdChipsCtrl.items.length}}"\
              aria-posinset="{{$index+1}}"\
              ng-click="!$mdChipsCtrl.readonly && $mdChipsCtrl.focusChip($index)"\
              aria-label="{{$mdChipsCtrl._isChipObject($chip) ? \'\' : $chip + \'. \'}}{{$mdChipsCtrl.isRemovable() ? \'\' + $mdChipsCtrl.deleteHint : \'\'}}" \
              ng-focus="!$mdChipsCtrl.readonly && $mdChipsCtrl.selectChip($index)"\
              md-chip-transclude="$mdChipsCtrl.chipContentsTemplate"></div>\
          <div ng-if="$mdChipsCtrl.isRemovable()"\
               class="md-chip-remove-container"\
               tabindex="-1"\
               md-chip-transclude="$mdChipsCtrl.chipRemoveTemplate"></div>\
        </md-chip>\
        <div class="md-chip-input-container" ng-if="!$mdChipsCtrl.readonly && $mdChipsCtrl.ngModelCtrl">\
          <div md-chip-transclude="$mdChipsCtrl.chipInputTemplate"></div>\
        </div>\
      </md-chips-wrap>';

  var CHIP_INPUT_TEMPLATE = '\
        <input\
            class="md-input"\
            tabindex="0"\
            aria-label="{{$mdChipsCtrl.inputAriaLabel}}"\
            placeholder="{{$mdChipsCtrl.getPlaceholder()}}"\
            ng-model="$mdChipsCtrl.chipBuffer"\
            ng-focus="$mdChipsCtrl.onInputFocus()"\
            ng-blur="$mdChipsCtrl.onInputBlur()"\
            ng-keydown="$mdChipsCtrl.inputKeydown($event)">';

  var CHIP_DEFAULT_TEMPLATE = '\
      <span>{{$chip}}</span>';

  var CHIP_REMOVE_TEMPLATE = '\
      <button\
          class="md-chip-remove"\
          ng-if="$mdChipsCtrl.isRemovable()"\
          ng-click="$mdChipsCtrl.removeChipAndFocusInput($$replacedScope.$index, $event)"\
          type="button"\
          tabindex="-1"\
          aria-label="{{$mdChipsCtrl.deleteButtonLabel}}{{$mdChipsCtrl._isChipObject($chip) ? \'\' : \' \' + $chip}}">\
        <md-icon md-svg-src="{{$mdChipsCtrl.mdCloseIcon}}" aria-hidden="true"></md-icon>\
      </button>';

  /**
   * MDChips Directive Definition
   */
  function MdChips ($mdTheming, $mdUtil, $compile, $log, $timeout, $$mdSvgRegistry) {
    // Run our templates through $mdUtil.processTemplate() to allow custom start/end symbols
    var templates = getTemplates();

    return {
      template: function(element, attrs) {
        // Clone the element into an attribute. By prepending the attribute
        // name with '$', AngularJS won't write it into the DOM. The cloned
        // element propagates to the link function via the attrs argument,
        // where various contained-elements can be consumed.
        attrs['$mdUserTemplate'] = element.clone();
        return templates.chips;
      },
      require: ['mdChips'],
      restrict: 'E',
      controller: 'MdChipsCtrl',
      controllerAs: '$mdChipsCtrl',
      bindToController: true,
      compile: compile,
      scope: {
        readonly: '=?readonly',
        removable: '=?mdRemovable',
        placeholder: '@?',
        secondaryPlaceholder: '@?',
        maxChips: '@?mdMaxChips',
        transformChip: '&mdTransformChip',
        onAppend: '&?mdOnAppend',
        onAdd: '&?mdOnAdd',
        onRemove: '&?mdOnRemove',
        addedMessage: '@?mdAddedMessage',
        removedMessage: '@?mdRemovedMessage',
        onSelect: '&?mdOnSelect',
        inputAriaDescribedBy: '@?inputAriaDescribedby',
        inputAriaLabelledBy: '@?inputAriaLabelledby',
        inputAriaLabel: '@?',
        containerHint: '@?',
        containerEmptyHint: '@?',
        deleteHint: '@?',
        deleteButtonLabel: '@?',
        separatorKeys: '=?mdSeparatorKeys',
        requireMatch: '=?mdRequireMatch',
        chipAppendDelayString: '@?mdChipAppendDelay',
        ngChange: '&?'
      }
    };

    /**
     * Builds the final template for `md-chips` and returns the postLink function.
     *
     * Building the template involves 3 key components:
     * static chips
     * chip template
     * input control
     *
     * If no `ng-model` is provided, only the static chip work needs to be done.
     *
     * If no user-passed `md-chip-template` exists, the default template is used. This resulting
     * template is appended to the chip content element.
     *
     * The remove button may be overridden by passing an element with an md-chip-remove attribute.
     *
     * If an `input` or `md-autocomplete` element is provided by the caller, it is set aside for
     * transclusion later. The transclusion happens in `postLink` as the parent scope is required.
     * If no user input is provided, a default one is appended to the input container node in the
     * template.
     *
     * Static Chips (i.e. `md-chip` elements passed from the caller) are gathered and set aside for
     * transclusion in the `postLink` function.
     *
     *
     * @param element
     * @param attr
     * @returns {Function}
     */
    function compile(element, attr) {
      // Grab the user template from attr and reset the attribute to null.
      var userTemplate = attr['$mdUserTemplate'];
      attr['$mdUserTemplate'] = null;

      var chipTemplate = getTemplateByQuery('md-chips>md-chip-template');

      var chipRemoveSelector = $mdUtil
        .prefixer()
        .buildList('md-chip-remove')
        .map(function(attr) {
          return 'md-chips>*[' + attr + ']';
        })
        .join(',');

      // Set the chip remove, chip contents and chip input templates. The link function will put
      // them on the scope for transclusion later.
      var chipRemoveTemplate   = getTemplateByQuery(chipRemoveSelector) || templates.remove,
          chipContentsTemplate = chipTemplate || templates.default,
          chipInputTemplate    = getTemplateByQuery('md-chips>md-autocomplete')
              || getTemplateByQuery('md-chips>input')
              || templates.input,
          staticChips = userTemplate.find('md-chip');

      // Warn of malformed template. See #2545
      if (userTemplate[0].querySelector('md-chip-template>*[md-chip-remove]')) {
        $log.warn('invalid placement of md-chip-remove within md-chip-template.');
      }

      function getTemplateByQuery (query) {
        if (!attr.ngModel) return;
        var element = userTemplate[0].querySelector(query);
        return element && element.outerHTML;
      }

      /**
       * Configures controller and transcludes.
       */
      return function postLink(scope, element, attrs, controllers) {
        $mdUtil.initOptionalProperties(scope, attr);

        $mdTheming(element);
        var mdChipsCtrl = controllers[0];
        if (chipTemplate) {
          // Chip editing functionality assumes we are using the default chip template.
          mdChipsCtrl.enableChipEdit = false;
        }

        mdChipsCtrl.chipContentsTemplate = chipContentsTemplate;
        mdChipsCtrl.chipRemoveTemplate   = chipRemoveTemplate;
        mdChipsCtrl.chipInputTemplate    = chipInputTemplate;

        mdChipsCtrl.mdCloseIcon = $$mdSvgRegistry.mdClose;

        element
            .attr({ tabindex: -1 })
            .on('focus', function () { mdChipsCtrl.onFocus(); })
            .on('click', function () {
              if (!mdChipsCtrl.readonly && mdChipsCtrl.selectedChip === -1) {
                mdChipsCtrl.onFocus();
              }
            });

        if (attr.ngModel) {
          mdChipsCtrl.configureNgModel(element.controller('ngModel'));

          // If an `md-transform-chip` attribute was set, tell the controller to use the expression
          // before appending chips.
          if (attrs.mdTransformChip) mdChipsCtrl.useTransformChipExpression();

          // If an `md-on-append` attribute was set, tell the controller to use the expression
          // when appending chips.
          //
          // TODO: Remove this now that 1.0 is long since released
          // DEPRECATED: Will remove in official 1.0 release
          if (attrs.mdOnAppend) mdChipsCtrl.useOnAppendExpression();

          // If an `md-on-add` attribute was set, tell the controller to use the expression
          // when adding chips.
          if (attrs.mdOnAdd) mdChipsCtrl.useOnAddExpression();

          // If an `md-on-remove` attribute was set, tell the controller to use the expression
          // when removing chips.
          if (attrs.mdOnRemove) mdChipsCtrl.useOnRemoveExpression();

          // If an `md-on-select` attribute was set, tell the controller to use the expression
          // when selecting chips.
          if (attrs.mdOnSelect) mdChipsCtrl.useOnSelectExpression();

          // The md-autocomplete and input elements won't be compiled until after this directive
          // is complete (due to their nested nature). Wait a tick before looking for them to
          // configure the controller.
          if (chipInputTemplate !== templates.input) {
            // The autocomplete will not appear until the readonly attribute is not true (i.e.
            // false or undefined), so we have to watch the readonly and then on the next tick
            // after the chip transclusion has run, we can configure the autocomplete and user
            // input.
            scope.$watch('$mdChipsCtrl.readonly', function(readonly) {
              if (!readonly) {

                $mdUtil.nextTick(function(){

                  if (chipInputTemplate.indexOf('<md-autocomplete') === 0) {
                    var autocompleteEl = element.find('md-autocomplete');
                    mdChipsCtrl.configureAutocomplete(autocompleteEl.controller('mdAutocomplete'));
                  }

                  mdChipsCtrl.configureUserInput(element.find('input'));
                });
              }
            });
          }

          // At the next tick, if we find an input, make sure it has the md-input class
          $mdUtil.nextTick(function() {
            var input = element.find('input');

            if (input) {
              mdChipsCtrl.configureInput(input);
              input.toggleClass('md-input', true);
            }
          });
        }

        // Compile with the parent's scope and prepend any static chips to the wrapper.
        if (staticChips.length > 0) {
          var compiledStaticChips = $compile(staticChips.clone())(scope.$parent);
          $timeout(function() { element.find('md-chips-wrap').prepend(compiledStaticChips); });
        }
      };
    }

    function getTemplates() {
      return {
        chips: $mdUtil.processTemplate(MD_CHIPS_TEMPLATE),
        input: $mdUtil.processTemplate(CHIP_INPUT_TEMPLATE),
        default: $mdUtil.processTemplate(CHIP_DEFAULT_TEMPLATE),
        remove: $mdUtil.processTemplate(CHIP_REMOVE_TEMPLATE)
      };
    }
  }

})();
(function(){
"use strict";


MdContactChipsCtrl.$inject = ["$attrs", "$element", "$timeout"];angular
    .module('material.components.chips')
    .controller('MdContactChipsCtrl', MdContactChipsCtrl);

/**
 * Controller for the MdContactChips component
 * @constructor
 */
function MdContactChipsCtrl ($attrs, $element, $timeout) {
  /** @type {$element} */
  this.$element = $element;

  /** @type {$attrs} */
  this.$attrs = $attrs;

  /** @type {Function} */
  this.$timeout = $timeout;

  /** @type {Object} */
  this.selectedItem = null;

  /** @type {string} */
  this.searchText = '';

  /**
   * Collection of functions to call to un-register watchers
   * @type {Array}
   */
  this.deRegister = [];

  this.init();
}

MdContactChipsCtrl.prototype.init = function() {
  var ctrl = this;
  var deRegister = this.deRegister;
  var element = this.$element;

  // Setup a watcher which manages chips a11y messages and autocomplete aria.
  // Timeout required to allow the child elements to be compiled.
  this.$timeout(function() {
    deRegister.push(
      element.find('md-chips').controller('mdChips').$scope.$watchCollection('$mdChipsCtrl.items', function() {
        // Make sure our input and wrapper have the correct ARIA attributes
        ctrl.setupChipsAria();
        ctrl.setupAutocompleteAria();
      })
    );
  });
};

MdContactChipsCtrl.prototype.setupChipsAria = function() {
  var chips = this.$element.find('md-chips');
  var chipsCtrl = chips.controller('mdChips');

  // Configure MdChipsCtrl
  if (this.removedMessage) {
    chipsCtrl.removedMessage = this.removedMessage;
  }
  if (this.containerHint) {
    chipsCtrl.containerHint = this.containerHint;
  }
  if (this.containerEmptyHint) {
    // Apply attribute to avoid the hint being overridden by MdChipsCtrl.configureAutocomplete()
    chips.attr('container-empty-hint', this.containerEmptyHint);
    chipsCtrl.containerEmptyHint = this.containerEmptyHint;
  }
  if (this.deleteHint) {
    chipsCtrl.deleteHint = this.deleteHint;
  }
  if (this.inputAriaLabel) {
    chipsCtrl.inputAriaLabel = this.inputAriaLabel;
  }
};

MdContactChipsCtrl.prototype.setupAutocompleteAria = function() {
  var autocompleteInput = this.$element.find('md-chips-wrap').find('md-autocomplete').find('input');

  // Set attributes on the input of the md-autocomplete
  if (this.inputAriaDescribedBy) {
    autocompleteInput.attr('aria-describedby', this.inputAriaDescribedBy);
  }
  if (this.inputAriaLabelledBy) {
    autocompleteInput.removeAttr('aria-label');
    autocompleteInput.attr('aria-labelledby', this.inputAriaLabelledBy);
  }
};

MdContactChipsCtrl.prototype.queryContact = function(searchText) {
  return this.contactQuery({'$query': searchText});
};

MdContactChipsCtrl.prototype.inputKeydown = function(event) {
  if (!this.separatorKeys || this.separatorKeys.indexOf(event.keyCode) < 0) {
    return;
  }

  event.stopPropagation();
  event.preventDefault();

  var autocompleteCtrl = angular.element(event.target).controller('mdAutocomplete');
  autocompleteCtrl.select(autocompleteCtrl.index);
};

MdContactChipsCtrl.prototype.itemName = function(item) {
  return item[this.contactName];
};

/**
 * Destructor for cleanup
 */
MdContactChipsCtrl.prototype.$onDestroy = function $onDestroy() {
  var $destroyFn;
  while (($destroyFn = this.deRegister.pop())) {
    $destroyFn.call(this);
  }
};

})();
(function(){
"use strict";


MdContactChips.$inject = ["$mdTheming", "$mdUtil"];angular
  .module('material.components.chips')
  .directive('mdContactChips', MdContactChips);

/**
 * @ngdoc directive
 * @name mdContactChips
 * @module material.components.chips
 *
 * @description
 * `<md-contact-chips>` is an input component based on `md-chips` and makes use of an
 * `md-autocomplete` element. The component allows the caller to supply a query expression which
 * returns  a list of possible contacts. The user can select one of these and add it to the list of
 * chips.
 *
 * You may also use the <a ng-href="api/directive/mdHighlightText">md-highlight-flags</a> attribute
 * along with its parameters to control the appearance of the matched text inside of the contacts'
 * autocomplete popup.
 *
 * @param {expression} ng-model Assignable AngularJS expression to be data-bound to the list of
 *    contact chips. The expression should evaluate to an `Object` Array.
 * @param {expression=} ng-change AngularJS expression to be executed on chip addition, removal,
 *    or content change.
 * @param {string=} placeholder Placeholder text that will be forwarded to the input.
 * @param {string=} secondary-placeholder Placeholder text that will be forwarded to the input,
 *    displayed when there is at least on item in the list
 * @param {expression} md-contacts An expression expected to return contacts matching the search
 *    test, `$query`. If this expression involves a promise, a loading bar is displayed while
 *    waiting for it to resolve.
 * @param {string} md-contact-name The field name of the contact object representing the
 *    contact's name.
 * @param {string} md-contact-email The field name of the contact object representing the
 *    contact's email address.
 * @param {string} md-contact-image The field name of the contact object representing the
 *    contact's image.
 * @param {number=} md-min-length Specifies the minimum length of text before autocomplete will
 *    make suggestions
 * @param {string=} input-aria-describedby A space-separated list of element IDs. This should
 *     contain the IDs of any elements that describe this autocomplete. Screen readers will read
 *     the content of these elements at the end of announcing that the chips input has been
 *     selected and describing its current state. The descriptive elements do not need to be
 *     visible on the page.
 * @param {string=} input-aria-labelledby A space-separated list of element IDs. The ideal use
 *    case is that this would contain the ID of a `<label>` element that is associated with these
 *    chips.<br><br>
 *    For `<label id="state">US State</label>`, you would set this to
 *    `input-aria-labelledby="state"`.
 * @param {string=} input-aria-label A string read by screen readers to identify the input.
 *    For static chips, this will be applied to the chips container.
 * @param {string=} container-hint A string read by screen readers informing users of how to
 *    navigate the chips when there are chips.
 * @param {string=} container-empty-hint A string read by screen readers informing users of how to
 *    add chips when there are no chips. You will want to use this to override the default when
 *    in a non-English locale.
 * @param {string=} delete-hint A string read by screen readers instructing users that pressing
 *    the delete key will remove the chip. You will want to use this to override the default when
 *    in a non-English locale.
 * @param {string=} md-removed-message Screen readers will announce this message following the
 *    chips contents. The default is `"removed"`. If a chip with the content of "Apple" was
 *    removed, the screen reader would read "Apple removed". You will want to use this to override
 *    the default when in a non-English locale.
 *
 *
 * @usage
 * <hljs lang="html">
 *   <md-contact-chips
 *       ng-model="ctrl.contacts"
 *       md-contacts="ctrl.querySearch($query)"
 *       md-contact-name="name"
 *       md-contact-image="image"
 *       md-contact-email="email"
 *       placeholder="To">
 *   </md-contact-chips>
 * </hljs>
 *
 */


var MD_CONTACT_CHIPS_TEMPLATE = '\
      <md-chips class="md-contact-chips"\
          ng-model="$mdContactChipsCtrl.contacts"\
          ng-change="$mdContactChipsCtrl.ngChange($mdContactChipsCtrl.contacts)"\
          md-require-match="$mdContactChipsCtrl.requireMatch"\
          md-chip-append-delay="{{$mdContactChipsCtrl.chipAppendDelay}}"\
          md-separator-keys="$mdContactChipsCtrl.separatorKeys"\
          md-autocomplete-snap>\
          <md-autocomplete\
              md-menu-class="md-contact-chips-suggestions"\
              md-selected-item="$mdContactChipsCtrl.selectedItem"\
              md-search-text="$mdContactChipsCtrl.searchText"\
              md-items="item in $mdContactChipsCtrl.queryContact($mdContactChipsCtrl.searchText)"\
              md-item-text="$mdContactChipsCtrl.itemName(item)"\
              md-no-cache="true"\
              md-min-length="$mdContactChipsCtrl.minLength"\
              md-autoselect\
              ng-keydown="$mdContactChipsCtrl.inputKeydown($event)"\
              placeholder="{{$mdContactChipsCtrl.contacts.length === 0 ?\
                  $mdContactChipsCtrl.placeholder : $mdContactChipsCtrl.secondaryPlaceholder}}">\
            <div class="md-contact-suggestion">\
              <img \
                  ng-src="{{item[$mdContactChipsCtrl.contactImage]}}"\
                  alt="{{item[$mdContactChipsCtrl.contactName]}}"\
                  ng-if="item[$mdContactChipsCtrl.contactImage]" />\
              <span class="md-contact-name" md-highlight-text="$mdContactChipsCtrl.searchText"\
                    md-highlight-flags="{{$mdContactChipsCtrl.highlightFlags}}">\
                {{item[$mdContactChipsCtrl.contactName]}}\
              </span>\
              <span class="md-contact-email" >{{item[$mdContactChipsCtrl.contactEmail]}}</span>\
            </div>\
          </md-autocomplete>\
          <md-chip-template>\
            <div class="md-contact-avatar">\
              <img \
                  ng-src="{{$chip[$mdContactChipsCtrl.contactImage]}}"\
                  alt="{{$chip[$mdContactChipsCtrl.contactName]}}"\
                  ng-if="$chip[$mdContactChipsCtrl.contactImage]" />\
            </div>\
            <div class="md-contact-name">\
              {{$chip[$mdContactChipsCtrl.contactName]}}\
            </div>\
          </md-chip-template>\
      </md-chips>';


/**
 * MDContactChips Directive Definition
 *
 * @param $mdTheming
 * @param $mdUtil
 * @returns {*}
 * @ngInject
 */
function MdContactChips($mdTheming, $mdUtil) {
  return {
    template: function(element, attrs) {
      return MD_CONTACT_CHIPS_TEMPLATE;
    },
    restrict: 'E',
    controller: 'MdContactChipsCtrl',
    controllerAs: '$mdContactChipsCtrl',
    bindToController: true,
    compile: compile,
    scope: {
      contactQuery: '&mdContacts',
      placeholder: '@?',
      secondaryPlaceholder: '@?',
      contactName: '@mdContactName',
      contactImage: '@mdContactImage',
      contactEmail: '@mdContactEmail',
      contacts: '=ngModel',
      ngChange: '&?',
      requireMatch: '=?mdRequireMatch',
      minLength: '=?mdMinLength',
      highlightFlags: '@?mdHighlightFlags',
      chipAppendDelay: '@?mdChipAppendDelay',
      separatorKeys: '=?mdSeparatorKeys',
      removedMessage: '@?mdRemovedMessage',
      inputAriaDescribedBy: '@?inputAriaDescribedby',
      inputAriaLabelledBy: '@?inputAriaLabelledby',
      inputAriaLabel: '@?',
      containerHint: '@?',
      containerEmptyHint: '@?',
      deleteHint: '@?'
    }
  };

  function compile(element, attr) {
    return function postLink(scope, element, attrs, controllers) {
      var contactChipsController = controllers;

      $mdUtil.initOptionalProperties(scope, attr);
      $mdTheming(element);

      element.attr('tabindex', '-1');

      attrs.$observe('mdChipAppendDelay', function(newValue) {
        contactChipsController.chipAppendDelay = newValue;
      });
    };
  }
}

})();
(function(){
"use strict";

(function () {
  "use strict";

  /**
   *  Use a RegExp to check if the `md-colors="<expression>"` is static string
   *  or one that should be observed and dynamically interpolated.
   */
  MdColorsDirective.$inject = ["$mdColors", "$mdUtil", "$log", "$parse"];
  MdColorsService.$inject = ["$mdTheming", "$mdUtil", "$log"];
  var STATIC_COLOR_EXPRESSION = /^{((\s|,)*?["'a-zA-Z-]+?\s*?:\s*?('|")[a-zA-Z0-9-.]*('|"))+\s*}$/;
  var colorPalettes = null;

  /**
   * @ngdoc module
   * @name material.components.colors
   *
   * @description
   * Define $mdColors service and a `md-colors=""` attribute directive
   */
  angular
    .module('material.components.colors', ['material.core'])
    .directive('mdColors', MdColorsDirective)
    .service('$mdColors', MdColorsService);

  /**
   * @ngdoc service
   * @name $mdColors
   * @module material.components.colors
   *
   * @description
   * By default, defining a theme does not make its colors available for applying to non AngularJS
   * Material elements. The `$mdColors` service is used by the `md-color` directive to convert a
   * set of color expressions to RGBA values and then apply those values to the element as CSS
   * property values.
   *
   * @usage
   * Getting a color based on a theme
   *
   *  <hljs lang="js">
   *    angular.controller('myCtrl', function ($mdColors) {
   *      var color = $mdColors.getThemeColor('myTheme-primary-900-0.5');
   *      ...
   *    });
   *  </hljs>
   *
   * Applying a color from a palette to an element
   * <hljs lang="js">
   *   app.directive('myDirective', function($mdColors) {
   *     return {
   *       ...
   *       link: function (scope, elem) {
   *         $mdColors.applyThemeColors(elem, {color: 'red-A200-0.2'});
   *       }
   *    }
   *   });
   * </hljs>
   */
  function MdColorsService($mdTheming, $mdUtil, $log) {
    colorPalettes = colorPalettes || Object.keys($mdTheming.PALETTES);

    // Publish service instance
    return {
      applyThemeColors: applyThemeColors,
      getThemeColor: getThemeColor,
      hasTheme: hasTheme
    };

    // ********************************************
    // Internal Methods
    // ********************************************

    /**
     * @ngdoc method
     * @name $mdColors#applyThemeColors
     *
     * @description
     * Lookup a set of colors by hue, theme, and palette, then apply those colors
     * with the provided opacity (via `rgba()`) to the specified CSS property.
     *
     * @param {angular.element} element the element to apply the styles to
     * @param {Object} colorExpression Keys are CSS properties and values are strings representing
     * the `theme-palette-hue-opacity` of the desired color. For example:
     * `{'color': 'red-A200-0.3', 'background-color': 'myTheme-primary-700-0.8'}`. Theme, hue, and
     * opacity are optional.
     */
    function applyThemeColors(element, colorExpression) {
      try {
        if (colorExpression) {
          // Assign the calculate RGBA color values directly as inline CSS
          element.css(interpolateColors(colorExpression));
        }
      } catch (e) {
        $log.error(e.message);
      }
    }

    /**
     * @ngdoc method
     * @name $mdColors#getThemeColor
     *
     * @description
     * Get a parsed RGBA color using a string representing the `theme-palette-hue-opacity` of the
     * desired color.
     *
     * @param {string} expression color expression like `'red-A200-0.3'` or
     *  `'myTheme-primary-700-0.8'`. Theme, hue, and opacity are optional.
     * @returns {string} a CSS color value like `rgba(211, 47, 47, 0.8)`
     */
    function getThemeColor(expression) {
      var color = extractColorOptions(expression);

      return parseColor(color);
    }

    /**
     * Return the parsed color
     * @param {{hue: *, theme: any, palette: *, opacity: (*|string|number)}} color hash map of color
     *  definitions
     * @param {boolean=} contrast whether use contrast color for foreground. Defaults to false.
     * @returns {string} rgba color string
     */
    function parseColor(color, contrast) {
      contrast = contrast || false;
      var rgbValues = $mdTheming.PALETTES[color.palette][color.hue];

      rgbValues = contrast ? rgbValues.contrast : rgbValues.value;

      return $mdUtil.supplant('rgba({0}, {1}, {2}, {3})',
        [rgbValues[0], rgbValues[1], rgbValues[2], rgbValues[3] || color.opacity]
      );
    }

    /**
     * Convert the color expression into an object with scope-interpolated values
     * Then calculate the rgba() values based on the theme color parts
     * @param {Object} themeColors json object, keys are css properties and values are string of
     * the wanted color, for example: `{color: 'red-A200-0.3'}`.
     * @return {Object} Hashmap of CSS properties with associated `rgba()` string values
     */
    function interpolateColors(themeColors) {
      var rgbColors = {};

      var hasColorProperty = themeColors.hasOwnProperty('color');

      angular.forEach(themeColors, function (value, key) {
        var color = extractColorOptions(value);
        var hasBackground = key.indexOf('background') > -1;

        rgbColors[key] = parseColor(color);
        if (hasBackground && !hasColorProperty) {
          rgbColors.color = parseColor(color, true);
        }
      });

      return rgbColors;
    }

    /**
     * Check if expression has defined theme
     * For instance:
     *   'myTheme-primary' => true
     *   'red-800' => false
     * @param {string} expression color expression like 'red-800', 'red-A200-0.3',
     *   'myTheme-primary', or 'myTheme-primary-400'
     * @return {boolean} true if the expression has a theme part, false otherwise.
     */
    function hasTheme(expression) {
      return angular.isDefined($mdTheming.THEMES[expression.split('-')[0]]);
    }

    /**
     * For the evaluated expression, extract the color parts into a hash map
     * @param {string} expression color expression like 'red-800', 'red-A200-0.3',
     *   'myTheme-primary', or 'myTheme-primary-400'
     * @returns {{hue: *, theme: any, palette: *, opacity: (*|string|number)}}
     */
    function extractColorOptions(expression) {
      var parts = expression.split('-');
      var hasTheme = angular.isDefined($mdTheming.THEMES[parts[0]]);
      var theme = hasTheme ? parts.splice(0, 1)[0] : $mdTheming.defaultTheme();

      return {
        theme: theme,
        palette: extractPalette(parts, theme),
        hue: extractHue(parts, theme),
        opacity: parts[2] || 1
      };
    }

    /**
     * Calculate the theme palette name
     * @param {Array} parts
     * @param {string} theme name
     * @return {string}
     */
    function extractPalette(parts, theme) {
      // If the next section is one of the palettes we assume it's a two word palette
      // Two word palette can be also written in camelCase, forming camelCase to dash-case

      var isTwoWord = parts.length > 1 && colorPalettes.indexOf(parts[1]) !== -1;
      var palette = parts[0].replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

      if (isTwoWord)  palette = parts[0] + '-' + parts.splice(1, 1);

      if (colorPalettes.indexOf(palette) === -1) {
        // If the palette is not in the palette list it's one of primary/accent/warn/background
        var scheme = $mdTheming.THEMES[theme].colors[palette];
        if (!scheme) {
          throw new Error($mdUtil.supplant(
            'mdColors: couldn\'t find \'{palette}\' in the palettes.',
            {palette: palette}));
        }
        palette = scheme.name;
      }

      return palette;
    }

    /**
     * @param {Array} parts
     * @param {string} theme name
     * @return {*}
     */
    function extractHue(parts, theme) {
      var themeColors = $mdTheming.THEMES[theme].colors;

      if (parts[1] === 'hue') {
        var hueNumber = parseInt(parts.splice(2, 1)[0], 10);

        if (hueNumber < 1 || hueNumber > 3) {
          throw new Error($mdUtil.supplant(
            'mdColors: \'hue-{hueNumber}\' is not a valid hue, can be only \'hue-1\', \'hue-2\' and \'hue-3\'',
            {hueNumber: hueNumber}));
        }
        parts[1] = 'hue-' + hueNumber;

        if (!(parts[0] in themeColors)) {
          throw new Error($mdUtil.supplant(
            'mdColors: \'hue-x\' can only be used with [{availableThemes}], but was used with \'{usedTheme}\'',
            {
            availableThemes: Object.keys(themeColors).join(', '),
            usedTheme: parts[0]
          }));
        }

        return themeColors[parts[0]].hues[parts[1]];
      }

      return parts[1] || themeColors[parts[0] in themeColors ? parts[0] : 'primary'].hues['default'];
    }
  }

  /**
   * @ngdoc directive
   * @name mdColors
   * @module material.components.colors
   *
   * @restrict A
   *
   * @description
   * `mdColors` directive will apply the theme-based color expression as RGBA CSS style values.
   *
   *   The format will be similar to the colors defined in the Sass files:
   *
   *   ## `[?theme]-[palette]-[?hue]-[?opacity]`
   *   - [theme]    - default value is the default theme
   *   - [palette]  - can be either palette name or primary/accent/warn/background
   *   - [hue]      - default is 500 (hue-x can be used with primary/accent/warn/background)
   *   - [opacity]  - default is 1
   *
   *
   *   > `?` indicates optional parameter
   *
   * @usage
   * <hljs lang="html">
   *   <div md-colors="{background: 'myTheme-accent-900-0.43'}">
   *     <div md-colors="{color: 'red-A100', 'border-color': 'primary-600'}">
   *       <span>Color demo</span>
   *     </div>
   *   </div>
   * </hljs>
   *
   * The `mdColors` directive will automatically watch for changes in the expression if it recognizes
   * an interpolation expression or a function. For performance options, you can use `::` prefix to
   * the `md-colors` expression to indicate a one-time data binding.
   *
   * <hljs lang="html">
   *   <md-card md-colors="::{background: '{{theme}}-primary-700'}">
   *   </md-card>
   * </hljs>
   */
  function MdColorsDirective($mdColors, $mdUtil, $log, $parse) {
    return {
      restrict: 'A',
      require: ['^?mdTheme'],
      compile: function (tElem, tAttrs) {
        var shouldWatch = shouldColorsWatch();

        return function (scope, element, attrs, ctrl) {
          var mdThemeController = ctrl[0];

          var lastColors = {};

          /**
           * @param {string=} theme
           * @return {Object} colors found in the specified theme
           */
          var parseColors = function (theme) {
            if (typeof theme !== 'string') {
              theme = '';
            }

            if (!attrs.mdColors) {
              attrs.mdColors = '{}';
            }

            /**
             * Json.parse() does not work because the keys are not quoted;
             * use $parse to convert to a hash map
             */
            var colors = $parse(attrs.mdColors)(scope);

            /**
             * If mdTheme is defined higher up the DOM tree,
             * we add mdTheme's theme to the colors which don't specify a theme.
             *
             * @example
             * <hljs lang="html">
             *   <div md-theme="myTheme">
             *     <div md-colors="{background: 'primary-600'}">
             *       <span md-colors="{background: 'mySecondTheme-accent-200'}">Color demo</span>
             *     </div>
             *   </div>
             * </hljs>
             *
             * 'primary-600' will be changed to 'myTheme-primary-600',
             * but 'mySecondTheme-accent-200' will not be changed since it has a theme defined.
             */
            if (mdThemeController) {
              Object.keys(colors).forEach(function (prop) {
                var color = colors[prop];
                if (!$mdColors.hasTheme(color)) {
                  colors[prop] = (theme || mdThemeController.$mdTheme) + '-' + color;
                }
              });
            }

            cleanElement(colors);

            return colors;
          };

          /**
           * @param {Object} colors
           */
          var cleanElement = function (colors) {
            if (!angular.equals(colors, lastColors)) {
              var keys = Object.keys(lastColors);

              if (lastColors.background && !keys.color) {
                keys.push('color');
              }

              keys.forEach(function (key) {
                element.css(key, '');
              });
            }

            lastColors = colors;
          };

          /**
           * Registering for mgTheme changes and asking mdTheme controller run our callback whenever
           * a theme changes.
           */
          var unregisterChanges = angular.noop;

          if (mdThemeController) {
            unregisterChanges = mdThemeController.registerChanges(function (theme) {
              $mdColors.applyThemeColors(element, parseColors(theme));
            });
          }

          scope.$on('$destroy', function () {
            unregisterChanges();
          });

          try {
            if (shouldWatch) {
              scope.$watch(parseColors, angular.bind(this,
                $mdColors.applyThemeColors, element
              ), true);
            }
            else {
              $mdColors.applyThemeColors(element, parseColors());
            }

          }
          catch (e) {
            $log.error(e.message);
          }

        };

        /**
         * @return {boolean}
         */
        function shouldColorsWatch() {
          // Simulate 1x binding and mark mdColorsWatch == false
          var rawColorExpression = tAttrs.mdColors;
          var bindOnce = rawColorExpression.indexOf('::') > -1;
          var isStatic = bindOnce ? true : STATIC_COLOR_EXPRESSION.test(tAttrs.mdColors);

          // Remove it for the postLink...
          tAttrs.mdColors = rawColorExpression.replace('::', '');

          var hasWatchAttr = angular.isDefined(tAttrs.mdColorsWatch);

          return (bindOnce || isStatic) ? false :
            hasWatchAttr ? $mdUtil.parseAttributeBoolean(tAttrs.mdColorsWatch) : true;
        }
      }
    };
  }
})();

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.content
 *
 * @description
 * Scrollable content
 */
mdContentDirective.$inject = ["$mdTheming"];
angular.module('material.components.content', [
  'material.core'
])
  .directive('mdContent', mdContentDirective);

/**
 * @ngdoc directive
 * @name mdContent
 * @module material.components.content
 *
 * @restrict E
 *
 * @description
 *
 * The `<md-content>` directive is a container element useful for scrollable content. It achieves
 * this by setting the CSS `overflow` property to `auto` so that content can properly scroll.
 *
 * In general, `<md-content>` components are not designed to be nested inside one another. If
 * possible, it is better to make them siblings. This often results in a better user experience as
 * having nested scrollbars may confuse the user.
 *
 * ## Troubleshooting
 *
 * In some cases, you may wish to apply the `md-no-momentum` class to ensure that Safari's
 * momentum scrolling is disabled. Momentum scrolling can cause flickering issues while scrolling
 * SVG icons and some other components.
 *
 * Additionally, we now also offer the `md-no-flicker` class which can be applied to any element
 * and uses a Webkit-specific filter of `blur(0px)` that forces GPU rendering of all elements
 * inside (which eliminates the flicker on iOS devices).
 *
 * _<b>Note:</b> Forcing an element to render on the GPU can have unintended side-effects, especially
 * related to the z-index of elements. Please use with caution and only on the elements needed._
 *
 * @usage
 *
 * Add the `[layout-padding]` attribute to make the content padded.
 *
 * <hljs lang="html">
 *  <md-content layout-padding>
 *      Lorem ipsum dolor sit amet, ne quod novum mei.
 *  </md-content>
 * </hljs>
 */

function mdContentDirective($mdTheming) {
  return {
    restrict: 'E',
    controller: ['$scope', '$element', ContentController],
    link: function(scope, element) {
      element.addClass('_md');     // private md component indicator for styling

      $mdTheming(element);
      scope.$broadcast('$mdContentLoaded', element);

      iosScrollFix(element[0]);
    }
  };

  function ContentController($scope, $element) {
    this.$scope = $scope;
    this.$element = $element;
  }
}

function iosScrollFix(node) {
  // IOS FIX:
  // If we scroll where there is no more room for the webview to scroll,
  // by default the webview itself will scroll up and down, this looks really
  // bad.  So if we are scrolling to the very top or bottom, add/subtract one
  angular.element(node).on('$md.pressdown', function(ev) {
    // Only touch events
    if (ev.pointer.type !== 't') return;
    // Don't let a child content's touchstart ruin it for us.
    if (ev.$materialScrollFixed) return;
    ev.$materialScrollFixed = true;

    if (node.scrollTop === 0) {
      node.scrollTop = 1;
    } else if (node.scrollHeight === node.scrollTop + node.offsetHeight) {
      node.scrollTop -= 1;
    }
  });
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.datepicker
 * @description Module for the datepicker component.
 */

angular.module('material.components.datepicker', [
  'material.core',
  'material.components.icon',
  'material.components.virtualRepeat'
]);

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc directive
   * @name mdCalendar
   * @module material.components.datepicker
   *
   * @param {Date} ng-model The component's model. Should be a Date object.
   * @param {Object=} ng-model-options Allows tuning of the way in which `ng-model` is being
   *  updated. Also allows for a timezone to be specified.
   *  <a href="https://docs.angularjs.org/api/ng/directive/ngModelOptions#usage">Read more at the
   *  ngModelOptions docs.</a>
   * @param {Date=} md-min-date Expression representing the minimum date.
   * @param {Date=} md-max-date Expression representing the maximum date.
   * @param {(function(Date): boolean)=} md-date-filter Function expecting a date and returning a
   *  boolean whether it can be selected in "day" mode or not.
   * @param {(function(Date): boolean)=} md-month-filter Function expecting a date and returning a
   *  boolean whether it can be selected in "month" mode or not.
   * @param {String=} md-current-view Current view of the calendar. Can be either "month" or "year".
   * @param {String=} md-mode Restricts the user to only selecting a value from a particular view.
   *  This option can be used if the user is only supposed to choose from a certain date type
   *  (e.g. only selecting the month). Can be either "month" or "day". **Note** that this will
   *  overwrite the `md-current-view` value.
   *
   * @description
   * `<md-calendar>` is a component that renders a calendar that can be used to select a date.
   * It is a part of the `<md-datepicker>` pane, however it can also be used on it's own.
   *
   * @usage
   *
   * <hljs lang="html">
   *   <md-calendar ng-model="birthday"></md-calendar>
   * </hljs>
   */
  CalendarCtrl.$inject = ["$element", "$scope", "$$mdDateUtil", "$mdUtil", "$mdConstant", "$mdTheming", "$$rAF", "$attrs", "$mdDateLocale", "$filter"];
  calendarDirective.$inject = ["inputDirective"];
  angular.module('material.components.datepicker')
    .directive('mdCalendar', calendarDirective);

  // TODO(jelbourn): Mac Cmd + left / right == Home / End
  // TODO(jelbourn): Refactor month element creation to use cloneNode (performance).
  // TODO(jelbourn): Define virtual scrolling constants (compactness) users can override.
  // TODO(jelbourn): Animated month transition on ng-model change (virtual-repeat)
  // TODO(jelbourn): Scroll snapping (virtual repeat)
  // TODO(jelbourn): Remove superfluous row from short months (virtual-repeat)
  // TODO(jelbourn): Month headers stick to top when scrolling.
  // TODO(jelbourn): Previous month opacity is lowered when partially scrolled out of view.
  // TODO(jelbourn): Support md-calendar standalone on a page (as a tabstop w/ aria-live
  //     announcement and key handling).
  // TODO Read-only calendar (not just date-picker).

  function calendarDirective(inputDirective) {
    return {
      template: function(tElement, tAttr) {
        // This allows the calendar to work, without a datepicker. This ensures that the virtual
        // repeater scrolls to the proper place on load by deferring the execution until the next
        // digest. It's necessary only if the calendar is used without a datepicker, otherwise it's
        // already wrapped in an ngIf.
        var extraAttrs = tAttr.hasOwnProperty('ngIf') ? '' : 'ng-if="calendarCtrl.isInitialized"';
        return '' +
          '<div ng-switch="calendarCtrl.currentView" ' + extraAttrs + '>' +
            '<md-calendar-year ng-switch-when="year"></md-calendar-year>' +
            '<md-calendar-month ng-switch-default></md-calendar-month>' +
          '</div>';
      },
      scope: {
        minDate: '=mdMinDate',
        maxDate: '=mdMaxDate',
        dateFilter: '=mdDateFilter',
        monthFilter: '=mdMonthFilter',

        // These need to be prefixed, because Angular resets
        // any changes to the value due to bindToController.
        _mode: '@mdMode',
        _currentView: '@mdCurrentView'
      },
      require: ['ngModel', 'mdCalendar'],
      controller: CalendarCtrl,
      controllerAs: 'calendarCtrl',
      bindToController: true,
      link: function(scope, element, attrs, controllers) {
        var ngModelCtrl = controllers[0];
        var mdCalendarCtrl = controllers[1];
        mdCalendarCtrl.configureNgModel(ngModelCtrl, inputDirective);
      }
    };
  }

  /**
   * Occasionally the hideVerticalScrollbar method might read an element's
   * width as 0, because it hasn't been laid out yet. This value will be used
   * as a fallback, in order to prevent scenarios where the element's width
   * would otherwise have been set to 0. This value is the "usual" width of a
   * calendar within a floating calendar pane.
   */
  var FALLBACK_WIDTH = 340;

  /** Next identifier for calendar instance. */
  var nextUniqueId = 0;

  /** Maps the `md-mode` values to their corresponding calendar views. */
  var MODE_MAP = {
    day: 'month',
    month: 'year'
  };

  /**
   * Controller for the mdCalendar component.
   * @ngInject @constructor
   */
  function CalendarCtrl($element, $scope, $$mdDateUtil, $mdUtil,
    $mdConstant, $mdTheming, $$rAF, $attrs, $mdDateLocale, $filter) {

    $mdTheming($element);

    /**
     * @final
     * @type {!JQLite}
     */
    this.$element = $element;

    /**
     * @final
     * @type {!angular.Scope}
     */
    this.$scope = $scope;

    /**
     * @final
     * @type {!angular.$attrs} Current attributes object for the element
     */
    this.$attrs = $attrs;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.$mdUtil = $mdUtil;

    /** @final */
    this.keyCode = $mdConstant.KEY_CODE;

    /** @final */
    this.$$rAF = $$rAF;

    /** @final */
    this.$mdDateLocale = $mdDateLocale;

    /** @final The built-in Angular date filter. */
    this.ngDateFilter = $filter('date');

    /**
     * @final
     * @type {Date}
     */
    this.today = this.dateUtil.createDateAtMidnight();

    /** @type {!ngModel.NgModelController} */
    this.ngModelCtrl = undefined;

    /** @type {string} Class applied to the selected date cell. */
    this.SELECTED_DATE_CLASS = 'md-calendar-selected-date';

    /** @type {string} Class applied to the cell for today. */
    this.TODAY_CLASS = 'md-calendar-date-today';

    /** @type {string} Class applied to the focused cell. */
    this.FOCUSED_DATE_CLASS = 'md-focus';

    /**
     * @final
     * @type {number} Unique ID for this calendar instance.
     */
    this.id = nextUniqueId++;

    /**
     * The date that is currently focused or showing in the calendar. This will initially be set
     * to the ng-model value if set, otherwise to today. It will be updated as the user navigates
     * to other months. The cell corresponding to the displayDate does not necesarily always have
     * focus in the document (such as for cases when the user is scrolling the calendar).
     * @type {Date}
     */
    this.displayDate = null;

    /**
     * Allows restricting the calendar to only allow selecting a month or a day.
     * @type {'month'|'day'|null}
     */
    this.mode = null;

    /**
     * The selected date. Keep track of this separately from the ng-model value so that we
     * can know, when the ng-model value changes, what the previous value was before it's updated
     * in the component's UI.
     *
     * @type {Date}
     */
    this.selectedDate = null;

    /**
     * The first date that can be rendered by the calendar. The default is taken
     * from the mdDateLocale provider and is limited by the mdMinDate.
     * @type {Date}
     */
    this.firstRenderableDate = null;

    /**
     * The last date that can be rendered by the calendar. The default comes
     * from the mdDateLocale provider and is limited by the maxDate.
     * @type {Date}
     */
    this.lastRenderableDate = null;

    /**
     * Used to toggle initialize the root element in the next digest.
     * @type {boolean}
     */
    this.isInitialized = false;

    /**
     * Cache for the  width of the element without a scrollbar. Used to hide the scrollbar later on
     * and to avoid extra reflows when switching between views.
     * @type {number}
     */
    this.width = 0;

    /**
     * Caches the width of the scrollbar in order to be used when hiding it and to avoid extra reflows.
     * @type {number}
     */
    this.scrollbarWidth = 0;

    // Unless the user specifies so, the calendar should not be a tab stop.
    // This is necessary because ngAria might add a tabindex to anything with an ng-model
    // (based on whether or not the user has turned that particular feature on/off).
    if (!$attrs.tabindex) {
      $element.attr('tabindex', '-1');
    }

    var boundKeyHandler = angular.bind(this, this.handleKeyEvent);

    // If use the md-calendar directly in the body without datepicker,
    // handleKeyEvent will disable other inputs on the page.
    // So only apply the handleKeyEvent on the body when the md-calendar inside datepicker,
    // otherwise apply on the calendar element only.

    var handleKeyElement;
    if ($element.parent().hasClass('md-datepicker-calendar')) {
      handleKeyElement = angular.element(document.body);
    } else {
      handleKeyElement = $element;
    }

    // Bind the keydown handler to the body, in order to handle cases where the focused
    // element gets removed from the DOM and stops propagating click events.
    handleKeyElement.on('keydown', boundKeyHandler);

    $scope.$on('$destroy', function() {
      handleKeyElement.off('keydown', boundKeyHandler);
    });

    // For AngularJS 1.4 and older, where there are no lifecycle hooks but bindings are pre-assigned,
    // manually call the $onInit hook.
    if (angular.version.major === 1 && angular.version.minor <= 4) {
      this.$onInit();
    }
  }

  /**
   * AngularJS Lifecycle hook for newer AngularJS versions.
   * Bindings are not guaranteed to have been assigned in the controller, but they are in the
   * $onInit hook.
   */
  CalendarCtrl.prototype.$onInit = function() {
    /**
     * The currently visible calendar view. Note the prefix on the scope value,
     * which is necessary, because the datepicker seems to reset the real one value if the
     * calendar is open, but the `currentView` on the datepicker's scope is empty.
     * @type {String}
     */
    if (this._mode && MODE_MAP.hasOwnProperty(this._mode)) {
      this.currentView = MODE_MAP[this._mode];
      this.mode = this._mode;
    } else {
      this.currentView = this._currentView || 'month';
      this.mode = null;
    }

    if (this.minDate && this.minDate > this.$mdDateLocale.firstRenderableDate) {
      this.firstRenderableDate = this.minDate;
    } else {
      this.firstRenderableDate = this.$mdDateLocale.firstRenderableDate;
    }

    if (this.maxDate && this.maxDate < this.$mdDateLocale.lastRenderableDate) {
      this.lastRenderableDate = this.maxDate;
    } else {
      this.lastRenderableDate = this.$mdDateLocale.lastRenderableDate;
    }
  };

  /**
   * Sets up the controller's reference to ngModelController.
   * @param {!ngModel.NgModelController} ngModelCtrl Instance of the ngModel controller.
   * @param {Object} inputDirective Config for Angular's `input` directive.
   */
  CalendarCtrl.prototype.configureNgModel = function(ngModelCtrl, inputDirective) {
    var self = this;
    self.ngModelCtrl = ngModelCtrl;

    // The component needs to be [type="date"] in order to be picked up by AngularJS.
    this.$attrs.$set('type', 'date');

    // Invoke the `input` directive link function, adding a stub for the element.
    // This allows us to re-use AngularJS' logic for setting the timezone via ng-model-options.
    // It works by calling the link function directly which then adds the proper `$parsers` and
    // `$formatters` to the NgModelController.
    inputDirective[0].link.pre(this.$scope, {
      on: angular.noop,
      val: angular.noop,
      0: {}
    }, this.$attrs, [ngModelCtrl]);

    ngModelCtrl.$render = function() {
      var value = this.$viewValue, convertedDate;

      // In the case where a conversion is needed, the $viewValue here will be a string like
      // "2020-05-10" instead of a Date object.
      if (!self.dateUtil.isValidDate(value)) {
        convertedDate = self.dateUtil.removeLocalTzAndReparseDate(new Date(this.$viewValue));
        if (self.dateUtil.isValidDate(convertedDate)) {
          value = convertedDate;
        }
      }

      // Notify the child scopes of any changes.
      self.$scope.$broadcast('md-calendar-parent-changed', value);

      // Set up the selectedDate if it hasn't been already.
      if (!self.selectedDate) {
        self.selectedDate = value;
      }

      // Also set up the displayDate.
      if (!self.displayDate) {
        self.displayDate = self.selectedDate || self.today;
      }
    };

    self.$mdUtil.nextTick(function() {
      self.isInitialized = true;
    });
  };

  /**
   * Sets the ng-model value for the calendar and emits a change event.
   * @param {Date} date new value for the calendar
   */
  CalendarCtrl.prototype.setNgModelValue = function(date) {
    var timezone = this.$mdUtil.getModelOption(this.ngModelCtrl, 'timezone');
    var value = this.dateUtil.createDateAtMidnight(date);
    this.focusDate(value);
    this.$scope.$emit('md-calendar-change', value);
    this.ngModelCtrl.$setViewValue(this.ngDateFilter(value, 'yyyy-MM-dd', timezone), 'default');
    this.ngModelCtrl.$render();
    return value;
  };

  /**
   * Sets the current view that should be visible in the calendar
   * @param {string} newView View name to be set.
   * @param {number|Date} time Date object or a timestamp for the new display date.
   */
  CalendarCtrl.prototype.setCurrentView = function(newView, time) {
    var self = this;

    self.$mdUtil.nextTick(function() {
      self.currentView = newView;

      if (time) {
        self.displayDate = angular.isDate(time) ? time : new Date(time);
      }
    });
  };

  /**
   * Focus the cell corresponding to the given date.
   * @param {Date=} date The date to be focused.
   */
  CalendarCtrl.prototype.focusDate = function(date) {
    if (this.dateUtil.isValidDate(date)) {
      var previousFocus = this.$element[0].querySelector('.' + this.FOCUSED_DATE_CLASS);
      if (previousFocus) {
        previousFocus.classList.remove(this.FOCUSED_DATE_CLASS);
      }

      var cellId = this.getDateId(date, this.currentView);
      var cell = document.getElementById(cellId);
      if (cell) {
        cell.classList.add(this.FOCUSED_DATE_CLASS);
        cell.focus();
        this.displayDate = date;
      }
    } else {
      var rootElement = this.$element[0].querySelector('[ng-switch]');

      if (rootElement) {
        rootElement.focus();
      }
    }
  };

  /**
   * Highlights a date cell on the calendar and changes the selected date.
   * @param {Date=} date Date to be marked as selected.
   */
  CalendarCtrl.prototype.changeSelectedDate = function(date) {
    var selectedDateClass = this.SELECTED_DATE_CLASS;
    var prevDateCell = this.$element[0].querySelector('.' + selectedDateClass);

    // Remove the selected class from the previously selected date, if any.
    if (prevDateCell) {
      prevDateCell.classList.remove(selectedDateClass);
      prevDateCell.setAttribute('aria-selected', 'false');
    }

    // Apply the select class to the new selected date if it is set.
    if (date) {
      var dateCell = document.getElementById(this.getDateId(date, this.currentView));
      if (dateCell) {
        dateCell.classList.add(selectedDateClass);
        dateCell.setAttribute('aria-selected', 'true');
      }
    }

    this.selectedDate = date;
  };

  /**
   * Normalizes the key event into an action name. The action will be broadcast
   * to the child controllers.
   * @param {KeyboardEvent} event
   * @returns {String} The action that should be taken, or null if the key
   * does not match a calendar shortcut.
   */
  CalendarCtrl.prototype.getActionFromKeyEvent = function(event) {
    var keyCode = this.keyCode;

    switch (event.which) {
      case keyCode.ENTER: return 'select';

      case keyCode.RIGHT_ARROW: return 'move-right';
      case keyCode.LEFT_ARROW: return 'move-left';

      case keyCode.DOWN_ARROW: return event.metaKey ? 'move-page-down' : 'move-row-down';
      case keyCode.UP_ARROW: return event.metaKey ? 'move-page-up' : 'move-row-up';

      case keyCode.PAGE_DOWN: return 'move-page-down';
      case keyCode.PAGE_UP: return 'move-page-up';

      case keyCode.HOME: return 'start';
      case keyCode.END: return 'end';

      default: return null;
    }
  };

  /**
   * Handles a key event in the calendar with the appropriate action. The action will either
   * be to select the focused date or to navigate to focus a new date.
   * @param {KeyboardEvent} event
   */
  CalendarCtrl.prototype.handleKeyEvent = function(event) {
    var self = this;

    this.$scope.$apply(function() {
      // Capture escape and emit back up so that a wrapping component
      // (such as a date-picker) can decide to close.
      if (event.which === self.keyCode.ESCAPE || event.which === self.keyCode.TAB) {
        self.$scope.$emit('md-calendar-close');

        if (event.which === self.keyCode.TAB) {
          event.preventDefault();
        }

        return;
      }

      // Broadcast the action that any child controllers should take.
      var action = self.getActionFromKeyEvent(event);
      if (action) {
        event.preventDefault();
        event.stopPropagation();
        self.$scope.$broadcast('md-calendar-parent-action', action);
      }
    });
  };

  /**
   * Hides the vertical scrollbar on the calendar scroller of a child controller by
   * setting the width on the calendar scroller and the `overflow: hidden` wrapper
   * around the scroller, and then setting a padding-right on the scroller equal
   * to the width of the browser's scrollbar.
   *
   * This will cause a reflow.
   *
   * @param {object} childCtrl The child controller whose scrollbar should be hidden.
   */
  CalendarCtrl.prototype.hideVerticalScrollbar = function(childCtrl) {
    var self = this;
    var element = childCtrl.$element[0];
    var scrollMask = element.querySelector('.md-calendar-scroll-mask');

    if (self.width > 0) {
      setWidth();
    } else {
      self.$$rAF(function() {
        var scroller = childCtrl.calendarScroller;

        self.scrollbarWidth = scroller.offsetWidth - scroller.clientWidth;
        self.width = element.querySelector('table').offsetWidth;
        setWidth();
      });
    }

    function setWidth() {
      var width = self.width || FALLBACK_WIDTH;
      var scrollbarWidth = self.scrollbarWidth;
      var scroller = childCtrl.calendarScroller;

      scrollMask.style.width = width + 'px';
      scroller.style.width = (width + scrollbarWidth) + 'px';
      scroller.style.paddingRight = scrollbarWidth + 'px';
    }
  };

  /**
   * Gets an identifier for a date unique to the calendar instance for internal
   * purposes. Not to be displayed.
   * @param {Date} date The date for which the id is being generated
   * @param {string} namespace Namespace for the id. (month, year etc.)
   * @returns {string}
   */
  CalendarCtrl.prototype.getDateId = function(date, namespace) {
    if (!namespace) {
      throw new Error('A namespace for the date id has to be specified.');
    }

    return [
      'md',
      this.id,
      namespace,
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ].join('-');
  };

  /**
   * Util to trigger an extra digest on a parent scope, in order to to ensure that
   * any child virtual repeaters have updated. This is necessary, because the virtual
   * repeater doesn't update the $index the first time around since the content isn't
   * in place yet. The case, in which this is an issue, is when the repeater has less
   * than a page of content (e.g. a month or year view has a min or max date).
   */
  CalendarCtrl.prototype.updateVirtualRepeat = function() {
    var scope = this.$scope;
    var virtualRepeatResizeListener = scope.$on('$md-resize-enable', function() {
      if (!scope.$$phase) {
        scope.$apply();
      }

      virtualRepeatResizeListener();
    });
  };
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  CalendarMonthCtrl.$inject = ["$element", "$scope", "$animate", "$q", "$$mdDateUtil", "$mdDateLocale"];
  angular.module('material.components.datepicker')
    .directive('mdCalendarMonth', calendarDirective);

  /**
   * Height of one calendar month tbody. This must be made known to the virtual-repeat and is
   * subsequently used for scrolling to specific months.
   */
  var TBODY_HEIGHT = 265;

  /**
   * Height of a calendar month with a single row. This is needed to calculate the offset for
   * rendering an extra month in virtual-repeat that only contains one row.
   */
  var TBODY_SINGLE_ROW_HEIGHT = 45;

  /** Private directive that represents a list of months inside the calendar. */
  function calendarDirective() {
    return {
      template:
        '<table aria-hidden="true" class="md-calendar-day-header"><thead></thead></table>' +
        '<div class="md-calendar-scroll-mask">' +
        '<md-virtual-repeat-container class="md-calendar-scroll-container" ' +
              'md-offset-size="' + (TBODY_SINGLE_ROW_HEIGHT - TBODY_HEIGHT) + '">' +
            '<table role="grid" tabindex="0" class="md-calendar" aria-readonly="true">' +
              '<tbody ' +
                  'md-calendar-month-body ' +
                  'role="rowgroup" ' +
                  'md-virtual-repeat="i in monthCtrl.items" ' +
                  'md-month-offset="$index" ' +
                  'class="md-calendar-month" ' +
                  'md-start-index="monthCtrl.getSelectedMonthIndex()" ' +
                  'md-item-size="' + TBODY_HEIGHT + '">' +

                // The <tr> ensures that the <tbody> will always have the
                // proper height, even if it's empty. If it's content is
                // compiled, the <tr> will be overwritten.
                '<tr aria-hidden="true" md-force-height="\'' + TBODY_HEIGHT + 'px\'"></tr>' +
              '</tbody>' +
            '</table>' +
          '</md-virtual-repeat-container>' +
        '</div>',
      require: ['^^mdCalendar', 'mdCalendarMonth'],
      controller: CalendarMonthCtrl,
      controllerAs: 'monthCtrl',
      bindToController: true,
      link: function(scope, element, attrs, controllers) {
        var calendarCtrl = controllers[0];
        var monthCtrl = controllers[1];
        monthCtrl.initialize(calendarCtrl);
      }
    };
  }

  /**
   * Controller for the calendar month component.
   * @ngInject @constructor
   */
  function CalendarMonthCtrl($element, $scope, $animate, $q,
    $$mdDateUtil, $mdDateLocale) {

    /** @final {!angular.JQLite} */
    this.$element = $element;

    /** @final {!angular.Scope} */
    this.$scope = $scope;

    /** @final {!angular.$animate} */
    this.$animate = $animate;

    /** @final {!angular.$q} */
    this.$q = $q;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.dateLocale = $mdDateLocale;

    /** @final {HTMLElement} */
    this.calendarScroller = $element[0].querySelector('.md-virtual-repeat-scroller');

    /** @type {boolean} */
    this.isInitialized = false;

    /** @type {boolean} */
    this.isMonthTransitionInProgress = false;

    var self = this;

    /**
     * Handles a click event on a date cell.
     * Created here so that every cell can use the same function instance.
     * @this {HTMLTableCellElement} The cell that was clicked.
     */
    this.cellClickHandler = function() {
      var timestamp = $$mdDateUtil.getTimestampFromNode(this);
      self.$scope.$apply(function() {
        // The timestamp has to be converted to a valid date.
        self.calendarCtrl.setNgModelValue(new Date(timestamp));
      });
    };

    /**
     * Handles click events on the month headers. Switches
     * the calendar to the year view.
     * @this {HTMLTableCellElement} The cell that was clicked.
     */
    this.headerClickHandler = function() {
      self.calendarCtrl.setCurrentView('year', $$mdDateUtil.getTimestampFromNode(this));
    };
  }

  /** Initialization **/

  /**
   * Initialize the controller by saving a reference to the calendar and
   * setting up the object that will be iterated by the virtual repeater.
   */
  CalendarMonthCtrl.prototype.initialize = function(calendarCtrl) {
    /**
     * Dummy array-like object for virtual-repeat to iterate over. The length is the total
     * number of months that can be viewed. We add 2 months: one to include the current month
     * and one for the last dummy month.
     *
     * This is shorter than ideal because of a (potential) Firefox bug
     * https://bugzilla.mozilla.org/show_bug.cgi?id=1181658.
     */

    this.items = {
      length: this.dateUtil.getMonthDistance(
        calendarCtrl.firstRenderableDate,
        calendarCtrl.lastRenderableDate
      ) + 2
    };

    this.calendarCtrl = calendarCtrl;
    this.attachScopeListeners();
    calendarCtrl.updateVirtualRepeat();

    // Fire the initial render, since we might have missed it the first time it fired.
    calendarCtrl.ngModelCtrl && calendarCtrl.ngModelCtrl.$render();
  };

  /**
   * Gets the "index" of the currently selected date as it would be in the virtual-repeat.
   * @returns {number} the "index" of the currently selected date
   */
  CalendarMonthCtrl.prototype.getSelectedMonthIndex = function() {
    var calendarCtrl = this.calendarCtrl;

    return this.dateUtil.getMonthDistance(
      calendarCtrl.firstRenderableDate,
      calendarCtrl.displayDate || calendarCtrl.selectedDate || calendarCtrl.today
    );
  };

  /**
   * Change the date that is being shown in the calendar. If the given date is in a different
   * month, the displayed month will be transitioned.
   * @param {Date} date
   */
  CalendarMonthCtrl.prototype.changeDisplayDate = function(date) {
    // Initialization is deferred until this function is called because we want to reflect
    // the starting value of ngModel.
    if (!this.isInitialized) {
      this.buildWeekHeader();
      this.calendarCtrl.hideVerticalScrollbar(this);
      this.isInitialized = true;
      return this.$q.when();
    }

    // If trying to show an invalid date or a transition is in progress, do nothing.
    if (!this.dateUtil.isValidDate(date) || this.isMonthTransitionInProgress) {
      return this.$q.when();
    }

    this.isMonthTransitionInProgress = true;
    var animationPromise = this.animateDateChange(date);

    this.calendarCtrl.displayDate = date;

    var self = this;
    animationPromise.then(function() {
      self.isMonthTransitionInProgress = false;
    });

    return animationPromise;
  };

  /**
   * Animates the transition from the calendar's current month to the given month.
   * @param {Date} date
   * @returns {angular.$q.Promise} The animation promise.
   */
  CalendarMonthCtrl.prototype.animateDateChange = function(date) {
    if (this.dateUtil.isValidDate(date)) {
      var monthDistance = this.dateUtil.getMonthDistance(this.calendarCtrl.firstRenderableDate, date);
      this.calendarScroller.scrollTop = monthDistance * TBODY_HEIGHT;
    }

    return this.$q.when();
  };

  /**
   * Builds and appends a day-of-the-week header to the calendar.
   * This should only need to be called once during initialization.
   */
  CalendarMonthCtrl.prototype.buildWeekHeader = function() {
    var firstDayOfWeek = this.dateLocale.firstDayOfWeek;
    var shortDays = this.dateLocale.shortDays;

    var row = document.createElement('tr');
    for (var i = 0; i < 7; i++) {
      var th = document.createElement('th');
      th.textContent = shortDays[(i + firstDayOfWeek) % 7];
      row.appendChild(th);
    }

    this.$element.find('thead').append(row);
  };

  /**
   * Attaches listeners for the scope events that are broadcast by the calendar.
   */
  CalendarMonthCtrl.prototype.attachScopeListeners = function() {
    var self = this;

    self.$scope.$on('md-calendar-parent-changed', function(event, value) {
      self.calendarCtrl.changeSelectedDate(value);
      self.changeDisplayDate(value);
    });

    self.$scope.$on('md-calendar-parent-action', angular.bind(this, this.handleKeyEvent));
  };

  /**
   * Handles the month-specific keyboard interactions.
   * @param {Object} event Scope event object passed by the calendar.
   * @param {String} action Action, corresponding to the key that was pressed.
   */
  CalendarMonthCtrl.prototype.handleKeyEvent = function(event, action) {
    var calendarCtrl = this.calendarCtrl;
    var displayDate = calendarCtrl.displayDate;

    if (action === 'select') {
      calendarCtrl.setNgModelValue(displayDate);
    } else {
      var date = null;
      var dateUtil = this.dateUtil;

      switch (action) {
        case 'move-right': date = dateUtil.incrementDays(displayDate, 1); break;
        case 'move-left': date = dateUtil.incrementDays(displayDate, -1); break;

        case 'move-page-down': date = dateUtil.incrementMonths(displayDate, 1); break;
        case 'move-page-up': date = dateUtil.incrementMonths(displayDate, -1); break;

        case 'move-row-down': date = dateUtil.incrementDays(displayDate, 7); break;
        case 'move-row-up': date = dateUtil.incrementDays(displayDate, -7); break;

        case 'start': date = dateUtil.getFirstDateOfMonth(displayDate); break;
        case 'end': date = dateUtil.getLastDateOfMonth(displayDate); break;
      }

      if (date) {
        date = this.dateUtil.clampDate(date, calendarCtrl.minDate, calendarCtrl.maxDate);

        this.changeDisplayDate(date).then(function() {
          calendarCtrl.focusDate(date);
        });
      }
    }
  };
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  mdCalendarMonthBodyDirective.$inject = ["$compile", "$$mdSvgRegistry"];
  CalendarMonthBodyCtrl.$inject = ["$element", "$$mdDateUtil", "$mdDateLocale"];
  angular.module('material.components.datepicker')
      .directive('mdCalendarMonthBody', mdCalendarMonthBodyDirective);

  /**
   * Private directive consumed by md-calendar-month. Having this directive lets the calender use
   * md-virtual-repeat and also cleanly separates the month DOM construction functions from
   * the rest of the calendar controller logic.
   * @ngInject
   */
  function mdCalendarMonthBodyDirective($compile, $$mdSvgRegistry) {
    var ARROW_ICON = $compile('<md-icon md-svg-src="' +
      $$mdSvgRegistry.mdTabsArrow + '"></md-icon>')({})[0];

    return {
      require: ['^^mdCalendar', '^^mdCalendarMonth', 'mdCalendarMonthBody'],
      scope: { offset: '=mdMonthOffset' },
      controller: CalendarMonthBodyCtrl,
      controllerAs: 'mdMonthBodyCtrl',
      bindToController: true,
      link: function(scope, element, attrs, controllers) {
        var calendarCtrl = controllers[0];
        var monthCtrl = controllers[1];
        var monthBodyCtrl = controllers[2];

        monthBodyCtrl.calendarCtrl = calendarCtrl;
        monthBodyCtrl.monthCtrl = monthCtrl;
        monthBodyCtrl.arrowIcon = ARROW_ICON.cloneNode(true);

        // The virtual-repeat re-uses the same DOM elements, so there are only a limited number
        // of repeated items that are linked, and then those elements have their bindings updated.
        // Since the months are not generated by bindings, we simply regenerate the entire thing
        // when the binding (offset) changes.
        scope.$watch(function() { return monthBodyCtrl.offset; }, function(offset) {
          if (angular.isNumber(offset)) {
            monthBodyCtrl.generateContent();
          }
        });
      }
    };
  }

  /**
   * Controller for a single calendar month.
   * @ngInject @constructor
   */
  function CalendarMonthBodyCtrl($element, $$mdDateUtil, $mdDateLocale) {
    /**
     * @final
     * @type {!JQLite}
     */
    this.$element = $element;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.dateLocale = $mdDateLocale;

    /** @type {Object} Reference to the month view. */
    this.monthCtrl = null;

    /** @type {Object} Reference to the calendar. */
    this.calendarCtrl = null;

    /**
     * Number of months from the start of the month "items" that the currently rendered month
     * occurs. Set via angular data binding.
     * @type {number|null}
     */
    this.offset = null;

    /**
     * Date cell to focus after appending the month to the document.
     * @type {HTMLElement}
     */
    this.focusAfterAppend = null;
  }

  /** Generate and append the content for this month to the directive element. */
  CalendarMonthBodyCtrl.prototype.generateContent = function() {
    var date = this.dateUtil.incrementMonths(this.calendarCtrl.firstRenderableDate, this.offset);

    this.$element
      .empty()
      .append(this.buildCalendarForMonth(date));

    if (this.focusAfterAppend) {
      this.focusAfterAppend.classList.add(this.calendarCtrl.FOCUSED_DATE_CLASS);
      this.focusAfterAppend = null;
    }
  };

  /**
   * Creates a single cell to contain a date in the calendar with all appropriate
   * attributes and classes added. If a date is given, the cell content will be set
   * based on the date.
   * @param {Date=} opt_date
   * @returns {HTMLElement}
   */
  CalendarMonthBodyCtrl.prototype.buildDateCell = function(opt_date) {
    var monthCtrl = this.monthCtrl;
    var calendarCtrl = this.calendarCtrl;

    // TODO(jelbourn): cloneNode is likely a faster way of doing this.
    var cell = document.createElement('td');
    cell.tabIndex = -1;
    cell.classList.add('md-calendar-date');
    cell.setAttribute('role', 'gridcell');

    if (opt_date) {
      cell.setAttribute('tabindex', '-1');
      cell.setAttribute('aria-label', this.dateLocale.longDateFormatter(opt_date));
      cell.id = calendarCtrl.getDateId(opt_date, 'month');

      // Use `data-timestamp` attribute because IE10 does not support the `dataset` property.
      cell.setAttribute('data-timestamp', opt_date.getTime());

      // TODO(jelourn): Doing these comparisons for class addition during generation might be slow.
      // It may be better to finish the construction and then query the node and add the class.
      if (this.dateUtil.isSameDay(opt_date, calendarCtrl.today)) {
        cell.classList.add(calendarCtrl.TODAY_CLASS);
      }

      if (this.dateUtil.isValidDate(calendarCtrl.selectedDate) &&
          this.dateUtil.isSameDay(opt_date, calendarCtrl.selectedDate)) {
        cell.classList.add(calendarCtrl.SELECTED_DATE_CLASS);
        cell.setAttribute('aria-selected', 'true');
      }

      var cellText = this.dateLocale.dates[opt_date.getDate()];

      if (this.isDateEnabled(opt_date)) {
        // Add a indicator for select, hover, and focus states.
        var selectionIndicator = document.createElement('span');
        selectionIndicator.classList.add('md-calendar-date-selection-indicator');
        selectionIndicator.textContent = cellText;
        cell.appendChild(selectionIndicator);
        cell.addEventListener('click', monthCtrl.cellClickHandler);

        if (calendarCtrl.displayDate && this.dateUtil.isSameDay(opt_date, calendarCtrl.displayDate)) {
          this.focusAfterAppend = cell;
        }
      } else {
        cell.classList.add('md-calendar-date-disabled');
        cell.textContent = cellText;
      }
    }

    return cell;
  };

  /**
   * Check whether date is in range and enabled
   * @param {Date=} opt_date
   * @return {boolean} Whether the date is enabled.
   */
  CalendarMonthBodyCtrl.prototype.isDateEnabled = function(opt_date) {
    return this.dateUtil.isDateWithinRange(opt_date,
          this.calendarCtrl.minDate, this.calendarCtrl.maxDate) &&
          (!angular.isFunction(this.calendarCtrl.dateFilter)
           || this.calendarCtrl.dateFilter(opt_date));
  };

  /**
   * Builds a `tr` element for the calendar grid.
   * @param rowNumber The week number within the month.
   * @returns {HTMLElement}
   */
  CalendarMonthBodyCtrl.prototype.buildDateRow = function(rowNumber) {
    var row = document.createElement('tr');
    row.setAttribute('role', 'row');

    // Because of an NVDA bug (with Firefox), the row needs an aria-label in order
    // to prevent the entire row being read aloud when the user moves between rows.
    // See http://community.nvda-project.org/ticket/4643.
    row.setAttribute('aria-label', this.dateLocale.weekNumberFormatter(rowNumber));

    return row;
  };

  /**
   * Builds the <tbody> content for the given date's month.
   * @param {Date=} opt_dateInMonth
   * @returns {DocumentFragment} A document fragment containing the <tr> elements.
   */
  CalendarMonthBodyCtrl.prototype.buildCalendarForMonth = function(opt_dateInMonth) {
    var date = this.dateUtil.isValidDate(opt_dateInMonth) ? opt_dateInMonth : new Date();

    var firstDayOfMonth = this.dateUtil.getFirstDateOfMonth(date);
    var firstDayOfTheWeek = this.getLocaleDay_(firstDayOfMonth);
    var numberOfDaysInMonth = this.dateUtil.getNumberOfDaysInMonth(date);

    // Store rows for the month in a document fragment so that we can append them all at once.
    var monthBody = document.createDocumentFragment();

    var rowNumber = 1;
    var row = this.buildDateRow(rowNumber);
    monthBody.appendChild(row);

    // If this is the final month in the list of items, only the first week should render,
    // so we should return immediately after the first row is complete and has been
    // attached to the body.
    var isFinalMonth = this.offset === this.monthCtrl.items.length - 1;

    // Add a label for the month. If the month starts on a Sun/Mon/Tues, the month label
    // goes on a row above the first of the month. Otherwise, the month label takes up the first
    // two cells of the first row.
    var blankCellOffset = 0;
    var monthLabelCell = document.createElement('td');
    var monthLabelCellContent = document.createElement('span');
    var calendarCtrl = this.calendarCtrl;

    monthLabelCellContent.textContent = this.dateLocale.monthHeaderFormatter(date);
    monthLabelCell.appendChild(monthLabelCellContent);
    monthLabelCell.classList.add('md-calendar-month-label');
    // If the entire month is after the max date, render the label as a disabled state.
    if (calendarCtrl.maxDate && firstDayOfMonth > calendarCtrl.maxDate) {
      monthLabelCell.classList.add('md-calendar-month-label-disabled');
    // If the user isn't supposed to be able to change views, render the
    // label as usual, but disable the clicking functionality.
    } else if (!calendarCtrl.mode) {
      monthLabelCell.addEventListener('click', this.monthCtrl.headerClickHandler);
      monthLabelCell.setAttribute('data-timestamp', firstDayOfMonth.getTime());
      monthLabelCell.setAttribute('aria-label', this.dateLocale.monthFormatter(date));
      monthLabelCell.classList.add('md-calendar-label-clickable');
      monthLabelCell.appendChild(this.arrowIcon.cloneNode(true));
    }

    if (firstDayOfTheWeek <= 2) {
      monthLabelCell.setAttribute('colspan', '7');

      var monthLabelRow = this.buildDateRow();
      monthLabelRow.appendChild(monthLabelCell);
      monthBody.insertBefore(monthLabelRow, row);

      if (isFinalMonth) {
        return monthBody;
      }
    } else {
      blankCellOffset = 3;
      monthLabelCell.setAttribute('colspan', '3');
      row.appendChild(monthLabelCell);
    }

    // Add a blank cell for each day of the week that occurs before the first of the month.
    // For example, if the first day of the month is a Tuesday, add blank cells for Sun and Mon.
    // The blankCellOffset is needed in cases where the first N cells are used by the month label.
    for (var i = blankCellOffset; i < firstDayOfTheWeek; i++) {
      row.appendChild(this.buildDateCell());
    }

    // Add a cell for each day of the month, keeping track of the day of the week so that
    // we know when to start a new row.
    var dayOfWeek = firstDayOfTheWeek;
    var iterationDate = firstDayOfMonth;
    for (var d = 1; d <= numberOfDaysInMonth; d++) {
      // If we've reached the end of the week, start a new row.
      if (dayOfWeek === 7) {
        // We've finished the first row, so we're done if this is the final month.
        if (isFinalMonth) {
          return monthBody;
        }
        dayOfWeek = 0;
        rowNumber++;
        row = this.buildDateRow(rowNumber);
        monthBody.appendChild(row);
      }

      iterationDate.setDate(d);
      var cell = this.buildDateCell(iterationDate);
      row.appendChild(cell);

      dayOfWeek++;
    }

    // Ensure that the last row of the month has 7 cells.
    while (row.childNodes.length < 7) {
      row.appendChild(this.buildDateCell());
    }

    // Ensure that all months have 6 rows. This is necessary for now because the virtual-repeat
    // requires that all items have exactly the same height.
    while (monthBody.childNodes.length < 6) {
      var whitespaceRow = this.buildDateRow();
      for (var j = 0; j < 7; j++) {
        whitespaceRow.appendChild(this.buildDateCell());
      }
      monthBody.appendChild(whitespaceRow);
    }

    return monthBody;
  };

  /**
   * Gets the day-of-the-week index for a date for the current locale.
   * @private
   * @param {Date} date
   * @returns {number} The column index of the date in the calendar.
   */
  CalendarMonthBodyCtrl.prototype.getLocaleDay_ = function(date) {
    return (date.getDay() + (7 - this.dateLocale.firstDayOfWeek)) % 7;
  };
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  CalendarYearCtrl.$inject = ["$element", "$scope", "$animate", "$q", "$$mdDateUtil", "$mdUtil"];
  angular.module('material.components.datepicker')
    .directive('mdCalendarYear', calendarDirective);

  /**
   * Height of one calendar year tbody. This must be made known to the virtual-repeat and is
   * subsequently used for scrolling to specific years.
   */
  var TBODY_HEIGHT = 88;

  /** Private component, representing a list of years in the calendar. */
  function calendarDirective() {
    return {
      template:
        '<div class="md-calendar-scroll-mask">' +
          '<md-virtual-repeat-container class="md-calendar-scroll-container">' +
            '<table role="grid" tabindex="0" class="md-calendar" aria-readonly="true">' +
              '<tbody ' +
                  'md-calendar-year-body ' +
                  'role="rowgroup" ' +
                  'md-virtual-repeat="i in yearCtrl.items" ' +
                  'md-year-offset="$index" class="md-calendar-year" ' +
                  'md-start-index="yearCtrl.getFocusedYearIndex()" ' +
                  'md-item-size="' + TBODY_HEIGHT + '">' +
                // The <tr> ensures that the <tbody> will have the proper
                // height, even though it may be empty.
                '<tr aria-hidden="true" md-force-height="\'' + TBODY_HEIGHT + 'px\'"></tr>' +
              '</tbody>' +
            '</table>' +
          '</md-virtual-repeat-container>' +
        '</div>',
      require: ['^^mdCalendar', 'mdCalendarYear'],
      controller: CalendarYearCtrl,
      controllerAs: 'yearCtrl',
      bindToController: true,
      link: function(scope, element, attrs, controllers) {
        var calendarCtrl = controllers[0];
        var yearCtrl = controllers[1];
        yearCtrl.initialize(calendarCtrl);
      }
    };
  }

  /**
   * Controller for the mdCalendar component.
   * @ngInject @constructor
   */
  function CalendarYearCtrl($element, $scope, $animate, $q, $$mdDateUtil, $mdUtil) {

    /** @final {!angular.JQLite} */
    this.$element = $element;

    /** @final {!angular.Scope} */
    this.$scope = $scope;

    /** @final {!angular.$animate} */
    this.$animate = $animate;

    /** @final {!angular.$q} */
    this.$q = $q;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final {HTMLElement} */
    this.calendarScroller = $element[0].querySelector('.md-virtual-repeat-scroller');

    /** @type {boolean} */
    this.isInitialized = false;

    /** @type {boolean} */
    this.isMonthTransitionInProgress = false;

    /** @final */
    this.$mdUtil = $mdUtil;

    var self = this;

    /**
     * Handles a click event on a date cell.
     * Created here so that every cell can use the same function instance.
     * @this {HTMLTableCellElement} The cell that was clicked.
     */
    this.cellClickHandler = function() {
      self.onTimestampSelected($$mdDateUtil.getTimestampFromNode(this));
    };
  }

  /**
   * Initialize the controller by saving a reference to the calendar and
   * setting up the object that will be iterated by the virtual repeater.
   */
  CalendarYearCtrl.prototype.initialize = function(calendarCtrl) {
    /**
     * Dummy array-like object for virtual-repeat to iterate over. The length is the total
     * number of years that can be viewed. We add 1 extra in order to include the current year.
     */

    this.items = {
      length: this.dateUtil.getYearDistance(
        calendarCtrl.firstRenderableDate,
        calendarCtrl.lastRenderableDate
      ) + 1
    };

    this.calendarCtrl = calendarCtrl;
    this.attachScopeListeners();
    calendarCtrl.updateVirtualRepeat();

    // Fire the initial render, since we might have missed it the first time it fired.
    calendarCtrl.ngModelCtrl && calendarCtrl.ngModelCtrl.$render();
  };

  /**
   * Gets the "index" of the currently selected date as it would be in the virtual-repeat.
   * @returns {number}
   */
  CalendarYearCtrl.prototype.getFocusedYearIndex = function() {
    var calendarCtrl = this.calendarCtrl;

    return this.dateUtil.getYearDistance(
      calendarCtrl.firstRenderableDate,
      calendarCtrl.displayDate || calendarCtrl.selectedDate || calendarCtrl.today
    );
  };

  /**
   * Change the date that is highlighted in the calendar.
   * @param {Date} date
   */
  CalendarYearCtrl.prototype.changeDate = function(date) {
    // Initialization is deferred until this function is called because we want to reflect
    // the starting value of ngModel.
    if (!this.isInitialized) {
      this.calendarCtrl.hideVerticalScrollbar(this);
      this.isInitialized = true;
      return this.$q.when();
    } else if (this.dateUtil.isValidDate(date) && !this.isMonthTransitionInProgress) {
      var self = this;
      var animationPromise = this.animateDateChange(date);

      self.isMonthTransitionInProgress = true;
      self.calendarCtrl.displayDate = date;

      return animationPromise.then(function() {
        self.isMonthTransitionInProgress = false;
      });
    }
  };

  /**
   * Animates the transition from the calendar's current month to the given month.
   * @param {Date} date
   * @returns {angular.$q.Promise} The animation promise.
   */
  CalendarYearCtrl.prototype.animateDateChange = function(date) {
    if (this.dateUtil.isValidDate(date)) {
      var monthDistance = this.dateUtil.getYearDistance(this.calendarCtrl.firstRenderableDate, date);
      this.calendarScroller.scrollTop = monthDistance * TBODY_HEIGHT;
    }

    return this.$q.when();
  };

  /**
   * Handles the year-view-specific keyboard interactions.
   * @param {Object} event Scope event object passed by the calendar.
   * @param {String} action Action, corresponding to the key that was pressed.
   */
  CalendarYearCtrl.prototype.handleKeyEvent = function(event, action) {
    var self = this;
    var calendarCtrl = self.calendarCtrl;
    var displayDate = calendarCtrl.displayDate;

    if (action === 'select') {
      self.changeDate(displayDate).then(function() {
        self.onTimestampSelected(displayDate);
      });
    } else {
      var date = null;
      var dateUtil = self.dateUtil;

      switch (action) {
        case 'move-right': date = dateUtil.incrementMonths(displayDate, 1); break;
        case 'move-left': date = dateUtil.incrementMonths(displayDate, -1); break;

        case 'move-row-down': date = dateUtil.incrementMonths(displayDate, 6); break;
        case 'move-row-up': date = dateUtil.incrementMonths(displayDate, -6); break;
      }

      if (date) {
        var min = calendarCtrl.minDate ? dateUtil.getFirstDateOfMonth(calendarCtrl.minDate) : null;
        var max = calendarCtrl.maxDate ? dateUtil.getFirstDateOfMonth(calendarCtrl.maxDate) : null;
        date = dateUtil.getFirstDateOfMonth(self.dateUtil.clampDate(date, min, max));

        self.changeDate(date).then(function() {
          calendarCtrl.focusDate(date);
        });
      }
    }
  };

  /**
   * Attaches listeners for the scope events that are broadcast by the calendar.
   */
  CalendarYearCtrl.prototype.attachScopeListeners = function() {
    var self = this;

    self.$scope.$on('md-calendar-parent-changed', function(event, value) {
      self.calendarCtrl.changeSelectedDate(value ? self.dateUtil.getFirstDateOfMonth(value) : value);
      self.changeDate(value);
    });

    self.$scope.$on('md-calendar-parent-action', angular.bind(self, self.handleKeyEvent));
  };

  /**
   * Handles the behavior when a date is selected. Depending on the `mode`
   * of the calendar, this can either switch back to the calendar view or
   * set the model value.
   * @param {number} timestamp The selected timestamp.
   */
  CalendarYearCtrl.prototype.onTimestampSelected = function(timestamp) {
    var calendarCtrl = this.calendarCtrl;

    if (calendarCtrl.mode) {
      this.$mdUtil.nextTick(function() {
        // The timestamp has to be converted to a valid date.
        calendarCtrl.setNgModelValue(new Date(timestamp));
      });
    } else {
      calendarCtrl.setCurrentView('month', timestamp);
    }
  };
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  CalendarYearBodyCtrl.$inject = ["$element", "$$mdDateUtil", "$mdDateLocale"];
  angular.module('material.components.datepicker')
      .directive('mdCalendarYearBody', mdCalendarYearDirective);

  /**
   * Private component, consumed by the md-calendar-year, which separates the DOM construction logic
   * and allows for the year view to use md-virtual-repeat.
   */
  function mdCalendarYearDirective() {
    return {
      require: ['^^mdCalendar', '^^mdCalendarYear', 'mdCalendarYearBody'],
      scope: { offset: '=mdYearOffset' },
      controller: CalendarYearBodyCtrl,
      controllerAs: 'mdYearBodyCtrl',
      bindToController: true,
      link: function(scope, element, attrs, controllers) {
        var calendarCtrl = controllers[0];
        var yearCtrl = controllers[1];
        var yearBodyCtrl = controllers[2];

        yearBodyCtrl.calendarCtrl = calendarCtrl;
        yearBodyCtrl.yearCtrl = yearCtrl;

        scope.$watch(function() { return yearBodyCtrl.offset; }, function(offset) {
          if (angular.isNumber(offset)) {
            yearBodyCtrl.generateContent();
          }
        });
      }
    };
  }

  /**
   * Controller for a single year.
   * @ngInject @constructor
   */
  function CalendarYearBodyCtrl($element, $$mdDateUtil, $mdDateLocale) {
    /**
     * @final
     * @type {!JQLite}
     */
    this.$element = $element;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.dateLocale = $mdDateLocale;

    /** @type {Object} Reference to the calendar. */
    this.calendarCtrl = null;

    /** @type {Object} Reference to the year view. */
    this.yearCtrl = null;

    /**
     * Number of months from the start of the month "items" that the currently rendered month
     * occurs. Set via angular data binding.
     * @type {number|null}
     */
    this.offset = null;

    /**
     * Date cell to focus after appending the month to the document.
     * @type {HTMLElement}
     */
    this.focusAfterAppend = null;
  }

  /** Generate and append the content for this year to the directive element. */
  CalendarYearBodyCtrl.prototype.generateContent = function() {
    var date = this.dateUtil.incrementYears(this.calendarCtrl.firstRenderableDate, this.offset);

    this.$element
      .empty()
      .append(this.buildCalendarForYear(date));

    if (this.focusAfterAppend) {
      this.focusAfterAppend.classList.add(this.calendarCtrl.FOCUSED_DATE_CLASS);
      this.focusAfterAppend = null;
    }
  };

  /**
   * Creates a single cell to contain a year in the calendar.
   * @param {number} year Four-digit year.
   * @param {number} month Zero-indexed month.
   * @returns {HTMLElement}
   */
  CalendarYearBodyCtrl.prototype.buildMonthCell = function(year, month) {
    var calendarCtrl = this.calendarCtrl;
    var yearCtrl = this.yearCtrl;
    var cell = this.buildBlankCell();

    // Represent this month/year as a date.
    var firstOfMonth = new Date(year, month, 1);
    cell.setAttribute('aria-label', this.dateLocale.monthFormatter(firstOfMonth));
    cell.id = calendarCtrl.getDateId(firstOfMonth, 'year');

    // Use `data-timestamp` attribute because IE10 does not support the `dataset` property.
    cell.setAttribute('data-timestamp', String(firstOfMonth.getTime()));

    if (this.dateUtil.isSameMonthAndYear(firstOfMonth, calendarCtrl.today)) {
      cell.classList.add(calendarCtrl.TODAY_CLASS);
    }

    if (this.dateUtil.isValidDate(calendarCtrl.selectedDate) &&
        this.dateUtil.isSameMonthAndYear(firstOfMonth, calendarCtrl.selectedDate)) {
      cell.classList.add(calendarCtrl.SELECTED_DATE_CLASS);
      cell.setAttribute('aria-selected', 'true');
    }

    var cellText = this.dateLocale.shortMonths[month];

    if (this.dateUtil.isMonthWithinRange(
          firstOfMonth, calendarCtrl.minDate, calendarCtrl.maxDate) &&
      (!angular.isFunction(calendarCtrl.monthFilter) ||
        calendarCtrl.monthFilter(firstOfMonth))) {
      var selectionIndicator = document.createElement('span');
      selectionIndicator.classList.add('md-calendar-date-selection-indicator');
      selectionIndicator.textContent = cellText;
      cell.appendChild(selectionIndicator);
      cell.addEventListener('click', yearCtrl.cellClickHandler);

      if (calendarCtrl.displayDate &&
          this.dateUtil.isSameMonthAndYear(firstOfMonth, calendarCtrl.displayDate)) {
        this.focusAfterAppend = cell;
      }
    } else {
      cell.classList.add('md-calendar-date-disabled');
      cell.textContent = cellText;
    }

    return cell;
  };

  /**
   * Builds a blank cell.
   * @return {HTMLElement}
   */
  CalendarYearBodyCtrl.prototype.buildBlankCell = function() {
    var cell = document.createElement('td');
    cell.tabIndex = -1;
    cell.classList.add('md-calendar-date');
    cell.setAttribute('role', 'gridcell');

    cell.setAttribute('tabindex', '-1');
    return cell;
  };

  /**
   * Builds the <tbody> content for the given year.
   * @param {Date} date Date for which the content should be built.
   * @returns {DocumentFragment} A document fragment containing the months within the year.
   */
  CalendarYearBodyCtrl.prototype.buildCalendarForYear = function(date) {
    // Store rows for the month in a document fragment so that we can append them all at once.
    var year = date.getFullYear();
    var yearBody = document.createDocumentFragment();

    var monthCell, i;
    // First row contains label and Jan-Jun.
    var firstRow = document.createElement('tr');
    var labelCell = document.createElement('td');
    labelCell.className = 'md-calendar-month-label';
    labelCell.textContent = String(year);
    firstRow.appendChild(labelCell);

    for (i = 0; i < 6; i++) {
      firstRow.appendChild(this.buildMonthCell(year, i));
    }
    yearBody.appendChild(firstRow);

    // Second row contains a blank cell and Jul-Dec.
    var secondRow = document.createElement('tr');
    secondRow.appendChild(this.buildBlankCell());
    for (i = 6; i < 12; i++) {
      secondRow.appendChild(this.buildMonthCell(year, i));
    }
    yearBody.appendChild(secondRow);

    return yearBody;
  };
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc service
   * @name $mdDateLocaleProvider
   * @module material.components.datepicker
   *
   * @description
   * The `$mdDateLocaleProvider` is the provider that creates the `$mdDateLocale` service.
   * This provider that allows the user to specify messages, formatters, and parsers for date
   * internationalization. The `$mdDateLocale` service itself is consumed by AngularJS Material
   * components that deal with dates
   * (i.e. <a ng-href="api/directive/mdDatepicker">mdDatepicker</a>).
   *
   * @property {Array<string>} months Array of month names (in order).
   * @property {Array<string>} shortMonths Array of abbreviated month names.
   * @property {Array<string>} days Array of the days of the week (in order).
   * @property {Array<string>} shortDays Array of abbreviated days of the week.
   * @property {Array<string>} dates Array of dates of the month. Only necessary for locales
   *  using a numeral system other than [1, 2, 3...].
   * @property {Array<string>} firstDayOfWeek The first day of the week. Sunday = 0, Monday = 1,
   *  etc.
   * @property {function(string): Date} parseDate Function that converts a date string to a Date
   *  object (the date portion).
   * @property {function(Date, string): string} formatDate Function to format a date object to a
   *  string. The datepicker directive also provides the time zone, if it was specified.
   * @property {function(Date): string} monthHeaderFormatter Function that returns the label for
   *  a month given a date.
   * @property {function(Date): string} monthFormatter Function that returns the full name of a month
   *  for a given date.
   * @property {function(number): string} weekNumberFormatter Function that returns a label for
   *  a week given the week number.
   * @property {function(Date): string} longDateFormatter Function that formats a date into a long
   *  `aria-label` that is read by the screen reader when the focused date changes.
   * @property {string} msgCalendar Translation of the label "Calendar" for the current locale.
   * @property {string} msgOpenCalendar Translation of the button label "Open calendar" for the
   *  current locale.
   * @property {Date} firstRenderableDate The date from which the datepicker calendar will begin
   *  rendering. Note that this will be ignored if a minimum date is set.
   *  Defaults to January 1st 1880.
   * @property {Date} lastRenderableDate The last date that will be rendered by the datepicker
   *  calendar. Note that this will be ignored if a maximum date is set.
   *  Defaults to January 1st 2130.
   * @property {function(string): boolean} isDateComplete Function to determine whether a string
   *  makes sense to be parsed to a `Date` object. Returns `true` if the date appears to be complete
   *  and parsing should occur. By default, this checks for 3 groups of text or numbers separated
   *  by delimiters. This means that by default, date strings must include a month, day, and year
   *  to be parsed and for the model to be updated.
   *
   * @usage
   * <hljs lang="js">
   * myAppModule.config(function($mdDateLocaleProvider) {
   *
   *     // Example of a French localization.
   *     $mdDateLocaleProvider.months = ['janvier', 'février', 'mars', ...];
   *     $mdDateLocaleProvider.shortMonths = ['janv', 'févr', 'mars', ...];
   *     $mdDateLocaleProvider.days = ['dimanche', 'lundi', 'mardi', ...];
   *     $mdDateLocaleProvider.shortDays = ['Di', 'Lu', 'Ma', ...];
   *
   *     // Can change week display to start on Monday.
   *     $mdDateLocaleProvider.firstDayOfWeek = 1;
   *
   *     // Optional.
   *     $mdDateLocaleProvider.dates = [1, 2, 3, 4, 5, 6, ...];
   *
   *     // Example uses moment.js to parse and format dates.
   *     $mdDateLocaleProvider.parseDate = function(dateString) {
   *       var m = moment(dateString, 'L', true);
   *       return m.isValid() ? m.toDate() : new Date(NaN);
   *     };
   *
   *     $mdDateLocaleProvider.formatDate = function(date) {
   *       var m = moment(date);
   *       return m.isValid() ? m.format('L') : '';
   *     };
   *
   *     // Allow only a day and month to be specified.
   *     // This is required if using the 'M/D' format with moment.js.
   *     $mdDateLocaleProvider.isDateComplete = function(dateString) {
   *       dateString = dateString.trim();
   *
   *       // Look for two chunks of content (either numbers or text) separated by delimiters.
   *       var re = /^(([a-zA-Z]{3,}|[0-9]{1,4})([ .,]+|[/-]))([a-zA-Z]{3,}|[0-9]{1,4})/;
   *       return re.test(dateString);
   *     };
   *
   *     $mdDateLocaleProvider.monthHeaderFormatter = function(date) {
   *       return myShortMonths[date.getMonth()] + ' ' + date.getFullYear();
   *     };
   *
   *     // In addition to date display, date components also need localized messages
   *     // for aria-labels for screen-reader users.
   *
   *     $mdDateLocaleProvider.weekNumberFormatter = function(weekNumber) {
   *       return 'Semaine ' + weekNumber;
   *     };
   *
   *     $mdDateLocaleProvider.msgCalendar = 'Calendrier';
   *     $mdDateLocaleProvider.msgOpenCalendar = 'Ouvrir le calendrier';
   *
   *     // You can also set when your calendar begins and ends.
   *     $mdDateLocaleProvider.firstRenderableDate = new Date(1776, 6, 4);
   *     $mdDateLocaleProvider.lastRenderableDate = new Date(2012, 11, 21);
   * });
   * </hljs>
   *
   */
  angular.module('material.components.datepicker').config(["$provide", function($provide) {
    // TODO(jelbourn): Assert provided values are correctly formatted. Need assertions.

    /** @constructor */
    function DateLocaleProvider() {
      /** Array of full month names. E.g., ['January', 'February', ...] */
      this.months = null;

      /** Array of abbreviated month names. E.g., ['Jan', 'Feb', ...] */
      this.shortMonths = null;

      /** Array of full day of the week names. E.g., ['Monday', 'Tuesday', ...] */
      this.days = null;

      /** Array of abbreviated dat of the week names. E.g., ['M', 'T', ...] */
      this.shortDays = null;

      /** Array of dates of a month (1 - 31). Characters might be different in some locales. */
      this.dates = null;

      /** Index of the first day of the week. 0 = Sunday, 1 = Monday, etc. */
      this.firstDayOfWeek = 0;

      /**
       * Function that converts the date portion of a Date to a string.
       * @type {(function(Date): string)}
       */
      this.formatDate = null;

      /**
       * Function that converts a date string to a Date object (the date portion)
       * @type {function(string): Date}
       */
      this.parseDate = null;

      /**
       * Function that formats a Date into a month header string.
       * @type {function(Date): string}
       */
      this.monthHeaderFormatter = null;

      /**
       * Function that formats a week number into a label for the week.
       * @type {function(number): string}
       */
      this.weekNumberFormatter = null;

      /**
       * Function that formats a date into a long aria-label that is read
       * when the focused date changes.
       * @type {function(Date): string}
       */
      this.longDateFormatter = null;

      /**
       * Function to determine whether a string makes sense to be
       * parsed to a Date object.
       * @type {function(string): boolean}
       */
      this.isDateComplete = null;

      /**
       * ARIA label for the calendar "dialog" used in the datepicker.
       * @type {string}
       */
      this.msgCalendar = '';

      /**
       * ARIA label for the datepicker's "Open calendar" buttons.
       * @type {string}
       */
      this.msgOpenCalendar = '';
    }

    /**
     * Factory function that returns an instance of the dateLocale service.
     * @ngInject
     * @param $locale
     * @param $filter
     * @returns {DateLocale}
     */
    DateLocaleProvider.prototype.$get = function($locale, $filter) {
      /**
       * Default date-to-string formatting function.
       * @param {!Date} date
       * @param {string=} timezone
       * @returns {string}
       */
      function defaultFormatDate(date, timezone) {
        if (!date) {
          return '';
        }

        // All of the dates created through ng-material *should* be set to midnight.
        // If we encounter a date where the localeTime shows at 11pm instead of midnight,
        // we have run into an issue with DST where we need to increment the hour by one:
        // var d = new Date(1992, 9, 8, 0, 0, 0);
        // d.toLocaleString(); // == "10/7/1992, 11:00:00 PM"
        var localeTime = date.toLocaleTimeString();
        var formatDate = date;
        if (date.getHours() === 0 &&
            (localeTime.indexOf('11:') !== -1 || localeTime.indexOf('23:') !== -1)) {
          formatDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 1, 0, 0);
        }

        return $filter('date')(formatDate, 'M/d/yyyy', timezone);
      }

      /**
       * Default string-to-date parsing function.
       * @param {string|number} dateString
       * @returns {!Date}
       */
      function defaultParseDate(dateString) {
        return new Date(dateString);
      }

      /**
       * Default function to determine whether a string makes sense to be
       * parsed to a Date object.
       *
       * This is very permissive and is just a basic sanity check to ensure that
       * things like single integers aren't able to be parsed into dates.
       * @param {string} dateString
       * @returns {boolean}
       */
      function defaultIsDateComplete(dateString) {
        dateString = dateString.trim();

        // Looks for three chunks of content (either numbers or text) separated
        // by delimiters.
        var re = /^(([a-zA-Z]{3,}|[0-9]{1,4})([ .,]+|[/-])){2}([a-zA-Z]{3,}|[0-9]{1,4})$/;
        return re.test(dateString);
      }

      /**
       * Default date-to-string formatter to get a month header.
       * @param {!Date} date
       * @returns {string}
       */
      function defaultMonthHeaderFormatter(date) {
        return service.shortMonths[date.getMonth()] + ' ' + date.getFullYear();
      }

      /**
       * Default formatter for a month.
       * @param {!Date} date
       * @returns {string}
       */
      function defaultMonthFormatter(date) {
        return service.months[date.getMonth()] + ' ' + date.getFullYear();
      }

      /**
       * Default week number formatter.
       * @param number
       * @returns {string}
       */
      function defaultWeekNumberFormatter(number) {
        return 'Week ' + number;
      }

      /**
       * Default formatter for date cell aria-labels.
       * @param {!Date} date
       * @returns {string}
       */
      function defaultLongDateFormatter(date) {
        // Example: 'Thursday June 18 2015'
        return [
          service.days[date.getDay()],
          service.months[date.getMonth()],
          service.dates[date.getDate()],
          date.getFullYear()
        ].join(' ');
      }

      // The default "short" day strings are the first character of each day,
      // e.g., "Monday" => "M".
      var defaultShortDays = $locale.DATETIME_FORMATS.SHORTDAY.map(function(day) {
        return day.substring(0, 1);
      });

      // The default dates are simply the numbers 1 through 31.
      var defaultDates = Array(32);
      for (var i = 1; i <= 31; i++) {
        defaultDates[i] = i;
      }

      // Default ARIA messages are in English (US).
      var defaultMsgCalendar = 'Calendar';
      var defaultMsgOpenCalendar = 'Open calendar';

      // Default start/end dates that are rendered in the calendar.
      var defaultFirstRenderableDate = new Date(1880, 0, 1);
      var defaultLastRendereableDate = new Date(defaultFirstRenderableDate.getFullYear() + 250, 0, 1);

      var service = {
        months: this.months || $locale.DATETIME_FORMATS.MONTH,
        shortMonths: this.shortMonths || $locale.DATETIME_FORMATS.SHORTMONTH,
        days: this.days || $locale.DATETIME_FORMATS.DAY,
        shortDays: this.shortDays || defaultShortDays,
        dates: this.dates || defaultDates,
        firstDayOfWeek: this.firstDayOfWeek || 0,
        formatDate: this.formatDate || defaultFormatDate,
        parseDate: this.parseDate || defaultParseDate,
        isDateComplete: this.isDateComplete || defaultIsDateComplete,
        monthHeaderFormatter: this.monthHeaderFormatter || defaultMonthHeaderFormatter,
        monthFormatter: this.monthFormatter || defaultMonthFormatter,
        weekNumberFormatter: this.weekNumberFormatter || defaultWeekNumberFormatter,
        longDateFormatter: this.longDateFormatter || defaultLongDateFormatter,
        msgCalendar: this.msgCalendar || defaultMsgCalendar,
        msgOpenCalendar: this.msgOpenCalendar || defaultMsgOpenCalendar,
        firstRenderableDate: this.firstRenderableDate || defaultFirstRenderableDate,
        lastRenderableDate: this.lastRenderableDate || defaultLastRendereableDate
      };

      return service;
    };
    DateLocaleProvider.prototype.$get.$inject = ["$locale", "$filter"];

    $provide.provider('$mdDateLocale', new DateLocaleProvider());
  }]);
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * Utility for performing date calculations to facilitate operation of the calendar and
   * datepicker.
   */
  angular.module('material.components.datepicker').factory('$$mdDateUtil', ["$mdDateLocale", function($mdDateLocale) {
    return {
      getFirstDateOfMonth: getFirstDateOfMonth,
      getNumberOfDaysInMonth: getNumberOfDaysInMonth,
      getDateInNextMonth: getDateInNextMonth,
      getDateInPreviousMonth: getDateInPreviousMonth,
      isInNextMonth: isInNextMonth,
      isInPreviousMonth: isInPreviousMonth,
      getDateMidpoint: getDateMidpoint,
      isSameMonthAndYear: isSameMonthAndYear,
      getWeekOfMonth: getWeekOfMonth,
      incrementDays: incrementDays,
      incrementMonths: incrementMonths,
      getLastDateOfMonth: getLastDateOfMonth,
      isSameDay: isSameDay,
      getMonthDistance: getMonthDistance,
      isValidDate: isValidDate,
      setDateTimeToMidnight: setDateTimeToMidnight,
      createDateAtMidnight: createDateAtMidnight,
      isDateWithinRange: isDateWithinRange,
      incrementYears: incrementYears,
      getYearDistance: getYearDistance,
      clampDate: clampDate,
      getTimestampFromNode: getTimestampFromNode,
      isMonthWithinRange: isMonthWithinRange,
      removeLocalTzAndReparseDate: removeLocalTzAndReparseDate
    };

    /**
     * Gets the first day of the month for the given date's month.
     * @param {Date} date
     * @returns {Date}
     */
    function getFirstDateOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    }

    /**
     * Gets the number of days in the month for the given date's month.
     * @param date
     * @returns {number}
     */
    function getNumberOfDaysInMonth(date) {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    }

    /**
     * Get an arbitrary date in the month after the given date's month.
     * @param date
     * @returns {Date}
     */
    function getDateInNextMonth(date) {
      return new Date(date.getFullYear(), date.getMonth() + 1, 1);
    }

    /**
     * Get an arbitrary date in the month before the given date's month.
     * @param date
     * @returns {Date}
     */
    function getDateInPreviousMonth(date) {
      return new Date(date.getFullYear(), date.getMonth() - 1, 1);
    }

    /**
     * Gets whether two dates have the same month and year.
     * @param {Date} d1
     * @param {Date} d2
     * @returns {boolean}
     */
    function isSameMonthAndYear(d1, d2) {
      return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
    }

    /**
     * Gets whether two dates are the same day (not not necessarily the same time).
     * @param {Date} d1
     * @param {Date} d2
     * @returns {boolean}
     */
    function isSameDay(d1, d2) {
      return d1.getDate() == d2.getDate() && isSameMonthAndYear(d1, d2);
    }

    /**
     * Gets whether a date is in the month immediately after some date.
     * @param {Date} startDate The date from which to compare.
     * @param {Date} endDate The date to check.
     * @returns {boolean}
     */
    function isInNextMonth(startDate, endDate) {
      var nextMonth = getDateInNextMonth(startDate);
      return isSameMonthAndYear(nextMonth, endDate);
    }

    /**
     * Gets whether a date is in the month immediately before some date.
     * @param {Date} startDate The date from which to compare.
     * @param {Date} endDate The date to check.
     * @returns {boolean}
     */
    function isInPreviousMonth(startDate, endDate) {
      var previousMonth = getDateInPreviousMonth(startDate);
      return isSameMonthAndYear(endDate, previousMonth);
    }

    /**
     * Gets the midpoint between two dates.
     * @param {Date} d1
     * @param {Date} d2
     * @returns {Date}
     */
    function getDateMidpoint(d1, d2) {
      return createDateAtMidnight((d1.getTime() + d2.getTime()) / 2);
    }

    /**
     * Gets the week of the month that a given date occurs in.
     * @param {Date} date
     * @returns {number} Index of the week of the month (zero-based).
     */
    function getWeekOfMonth(date) {
      var firstDayOfMonth = getFirstDateOfMonth(date);
      return Math.floor((firstDayOfMonth.getDay() + date.getDate() - 1) / 7);
    }

    /**
     * Gets a new date incremented by the given number of days. Number of days can be negative.
     * @param {Date} date
     * @param {number} numberOfDays
     * @returns {Date}
     */
    function incrementDays(date, numberOfDays) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate() + numberOfDays);
    }

    /**
     * Gets a new date incremented by the given number of months. Number of months can be negative.
     * If the date of the given month does not match the target month, the date will be set to the
     * last day of the month.
     * @param {Date} date
     * @param {number} numberOfMonths
     * @returns {Date}
     */
    function incrementMonths(date, numberOfMonths) {
      // If the same date in the target month does not actually exist, the Date object will
      // automatically advance *another* month by the number of missing days.
      // For example, if you try to go from Jan. 30 to Feb. 30, you'll end up on March 2.
      // So, we check if the month overflowed and go to the last day of the target month instead.
      var dateInTargetMonth = new Date(date.getFullYear(), date.getMonth() + numberOfMonths, 1);
      var numberOfDaysInMonth = getNumberOfDaysInMonth(dateInTargetMonth);
      if (numberOfDaysInMonth < date.getDate()) {
        dateInTargetMonth.setDate(numberOfDaysInMonth);
      } else {
        dateInTargetMonth.setDate(date.getDate());
      }

      return dateInTargetMonth;
    }

    /**
     * Get the integer distance between two months. This *only* considers the month and year
     * portion of the Date instances.
     *
     * @param {Date} start
     * @param {Date} end
     * @returns {number} Number of months between `start` and `end`. If `end` is before `start`
     *     chronologically, this number will be negative.
     */
    function getMonthDistance(start, end) {
      return (12 * (end.getFullYear() - start.getFullYear())) + (end.getMonth() - start.getMonth());
    }

    /**
     * Gets the last day of the month for the given date.
     * @param {Date} date
     * @returns {Date}
     */
    function getLastDateOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), getNumberOfDaysInMonth(date));
    }

    /**
     * Checks whether a date is valid.
     * @param {Date} date
     * @return {boolean} Whether the date is a valid Date.
     */
    function isValidDate(date) {
      return date && date.getTime && !isNaN(date.getTime());
    }

    /**
     * Sets a date's time to midnight.
     * @param {Date} date
     */
    function setDateTimeToMidnight(date) {
      if (isValidDate(date)) {
        date.setHours(0, 0, 0, 0);
      }
    }

    /**
     * Creates a date with the time set to midnight.
     * Drop-in replacement for two forms of the Date constructor via opt_value.
     * @param {number|Date=} opt_value Leave undefined for a Date representing now. Or use a
     *  single value representing the number of seconds since the Unix Epoch or a Date object.
     * @return {Date} New date with time set to midnight.
     */
    function createDateAtMidnight(opt_value) {
      var date;
      if (angular.isDate(opt_value)) {
        date = opt_value;
      } else if (angular.isNumber(opt_value)) {
        date = new Date(opt_value);
      } else {
        date = new Date();
      }
      setDateTimeToMidnight(date);
      return date;
    }

     /**
      * Checks if a date is within a min and max range, ignoring the time component.
      * If minDate or maxDate are not dates, they are ignored.
      * @param {Date} date
      * @param {Date} minDate
      * @param {Date} maxDate
      */
     function isDateWithinRange(date, minDate, maxDate) {
       var dateAtMidnight = createDateAtMidnight(date);
       var minDateAtMidnight = isValidDate(minDate) ? createDateAtMidnight(minDate) : null;
       var maxDateAtMidnight = isValidDate(maxDate) ? createDateAtMidnight(maxDate) : null;
       return (!minDateAtMidnight || minDateAtMidnight <= dateAtMidnight) &&
           (!maxDateAtMidnight || maxDateAtMidnight >= dateAtMidnight);
     }

    /**
     * Gets a new date incremented by the given number of years. Number of years can be negative.
     * See `incrementMonths` for notes on overflow for specific dates.
     * @param {Date} date
     * @param {number} numberOfYears
     * @returns {Date}
     */
     function incrementYears(date, numberOfYears) {
       return incrementMonths(date, numberOfYears * 12);
     }

     /**
      * Get the integer distance between two years. This *only* considers the year portion of the
      * Date instances.
      *
      * @param {Date} start
      * @param {Date} end
      * @returns {number} Number of months between `start` and `end`. If `end` is before `start`
      *     chronologically, this number will be negative.
      */
     function getYearDistance(start, end) {
       return end.getFullYear() - start.getFullYear();
     }

     /**
      * Clamps a date between a minimum and a maximum date.
      * @param {Date} date Date to be clamped
      * @param {Date=} minDate Minimum date
      * @param {Date=} maxDate Maximum date
      * @return {Date}
      */
     function clampDate(date, minDate, maxDate) {
       var boundDate = date;
       if (minDate && date < minDate) {
         boundDate = new Date(minDate.getTime());
       }
       if (maxDate && date > maxDate) {
         boundDate = new Date(maxDate.getTime());
       }
       return boundDate;
     }

     /**
      * Extracts and parses the timestamp from a DOM node.
      * @param  {HTMLElement} node Node from which the timestamp will be extracted.
      * @return {number} Time since epoch.
      */
     function getTimestampFromNode(node) {
       if (node && node.hasAttribute('data-timestamp')) {
         return Number(node.getAttribute('data-timestamp'));
       }
     }

     /**
      * Checks if a month is within a min and max range, ignoring the date and time components.
      * If minDate or maxDate are not dates, they are ignored.
      * @param {Date} date
      * @param {Date} minDate
      * @param {Date} maxDate
      */
     function isMonthWithinRange(date, minDate, maxDate) {
       var month = date.getMonth();
       var year = date.getFullYear();

       return (!minDate || minDate.getFullYear() < year || minDate.getMonth() <= month) &&
        (!maxDate || maxDate.getFullYear() > year || maxDate.getMonth() >= month);
     }

    /**
     * @param {Date} value
     * @return {boolean|boolean}
     */
    function removeLocalTzAndReparseDate(value) {
      var dateValue, formattedDate;
      // Remove the local timezone offset before calling formatDate.
      dateValue = new Date(value.getTime() + 60000 * value.getTimezoneOffset());
      formattedDate = $mdDateLocale.formatDate(dateValue);
      // parseDate only works with a date formatted by formatDate when using Moment validation.
      return $mdDateLocale.parseDate(formattedDate);
    }
  }]);
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  // TODO(jelbourn): forward more attributes to the internal input (required, autofocus, etc.)
  // TODO(jelbourn): something better for mobile (calendar panel takes up entire screen?)
  // TODO(jelbourn): input behavior (masking? auto-complete?)

  DatePickerCtrl.$inject = ["$scope", "$element", "$attrs", "$window", "$mdConstant", "$mdTheming", "$mdUtil", "$mdDateLocale", "$$mdDateUtil", "$$rAF", "$filter", "$timeout"];
  datePickerDirective.$inject = ["$$mdSvgRegistry", "$mdUtil", "$mdAria", "inputDirective"];
  angular.module('material.components.datepicker')
      .directive('mdDatepicker', datePickerDirective);

  /**
   * @ngdoc directive
   * @name mdDatepicker
   * @module material.components.datepicker
   *
   * @param {Date} ng-model The component's model. Expects either a JavaScript Date object or a
   *  value that can be parsed into one (e.g. a ISO 8601 string).
   * @param {Object=} ng-model-options Allows tuning of the way in which `ng-model` is being
   *  updated. Also allows for a timezone to be specified.
   *  <a href="https://docs.angularjs.org/api/ng/directive/ngModelOptions#usage">
   *    Read more at the ngModelOptions docs.</a>
   * @param {expression=} ng-change Expression evaluated when the model value changes.
   * @param {expression=} ng-focus Expression evaluated when the input is focused or the calendar
   *  is opened.
   * @param {expression=} ng-blur Expression evaluated when focus is removed from the input or the
   *  calendar is closed.
   * @param {boolean=} ng-disabled Whether the datepicker is disabled.
   * @param {boolean=} ng-required Whether a value is required for the datepicker.
   * @param {Date=} md-min-date Expression representing a min date (inclusive).
   * @param {Date=} md-max-date Expression representing a max date (inclusive).
   * @param {(function(Date): boolean)=} md-date-filter Function expecting a date and returning a
   *  boolean whether it can be selected in "day" mode or not. Returning false will also trigger a
   *  `filtered` model validation error.
   * @param {(function(Date): boolean)=} md-month-filter Function expecting a date and returning a
   *  boolean whether it can be selected in "month" mode or not. Returning false will also trigger a
   *  `filtered` model validation error.
   * @param {String=} md-placeholder The date input placeholder value.
   * @param {String=} md-open-on-focus When present, the calendar will be opened when the input
   *  is focused.
   * @param {Boolean=} md-is-open Expression that can be used to open the datepicker's calendar
   *  on-demand.
   * @param {String=} md-current-view Default open view of the calendar pane. Can be either
   *  "month" or "year".
   * @param {String=} md-mode Restricts the user to only selecting a value from a particular view.
   *  This option can be used if the user is only supposed to choose from a certain date type
   *  (e.g. only selecting the month).
   * Can be either "month" or "day". **Note** that this will overwrite the `md-current-view` value.
   *
   * @param {String=} md-hide-icons Determines which datepicker icons should be hidden. Note that
   *  this may cause the datepicker to not align properly with other components.
   *  **Use at your own risk.** Possible values are:
   * * `"all"` - Hides all icons.
   * * `"calendar"` - Only hides the calendar icon.
   * * `"triangle"` - Only hides the triangle icon.
   * @param {Object=} md-date-locale Allows for the values from the `$mdDateLocaleProvider` to be
   * overwritten on a per-element basis (e.g. `msgOpenCalendar` can be overwritten with
   * `md-date-locale="{ msgOpenCalendar: 'Open a special calendar' }"`).
   *
   * @description
   * `<md-datepicker>` is a component used to select a single date.
   * For information on how to configure internationalization for the date picker,
   * see <a ng-href="api/service/$mdDateLocaleProvider">$mdDateLocaleProvider</a>.
   *
   * This component supports
   * [ngMessages](https://docs.angularjs.org/api/ngMessages/directive/ngMessages).
   * Supported attributes are:
   * * `required`: whether a required date is not set.
   * * `mindate`: whether the selected date is before the minimum allowed date.
   * * `maxdate`: whether the selected date is after the maximum allowed date.
   * * `debounceInterval`: ms to delay input processing (since last debounce reset);
   *    default value 500ms
   *
   * @usage
   * <hljs lang="html">
   *   <md-datepicker ng-model="birthday"></md-datepicker>
   * </hljs>
   *
   */

  function datePickerDirective($$mdSvgRegistry, $mdUtil, $mdAria, inputDirective) {
    return {
      template: function(tElement, tAttrs) {
        // Buttons are not in the tab order because users can open the calendar via keyboard
        // interaction on the text input, and multiple tab stops for one component (picker)
        // may be confusing.
        var hiddenIcons = tAttrs.mdHideIcons;
        var ariaLabelValue = tAttrs.ariaLabel || tAttrs.mdPlaceholder;
        var ngModelOptions = tAttrs.ngModelOptions;

        var calendarButton = (hiddenIcons === 'all' || hiddenIcons === 'calendar') ? '' :
          '<md-button class="md-datepicker-button md-icon-button" type="button" ' +
              'tabindex="-1" aria-hidden="true" ' +
              'ng-click="ctrl.openCalendarPane($event)">' +
            '<md-icon class="md-datepicker-calendar-icon" aria-label="md-calendar" ' +
                     'md-svg-src="' + $$mdSvgRegistry.mdCalendar + '"></md-icon>' +
          '</md-button>';

        var triangleButton = '';

        if (hiddenIcons !== 'all' && hiddenIcons !== 'triangle') {
          triangleButton = '' +
            '<md-button type="button" md-no-ink ' +
              'class="md-datepicker-triangle-button md-icon-button" ' +
              'ng-click="ctrl.openCalendarPane($event)" ' +
              'aria-label="{{::ctrl.locale.msgOpenCalendar}}">' +
            '<div class="md-datepicker-expand-triangle"></div>' +
          '</md-button>';

          tElement.addClass(HAS_TRIANGLE_ICON_CLASS);
        }

        return calendarButton +
        '<div class="md-datepicker-input-container" ng-class="{\'md-datepicker-focused\': ctrl.isFocused}">' +
          '<input ' +
            (ariaLabelValue ? 'aria-label="' + ariaLabelValue + '" ' : '') +
            'class="md-datepicker-input" ' +
            'aria-haspopup="dialog" ' +
            'ng-focus="ctrl.setFocused(true)" ' +
            'ng-blur="ctrl.setFocused(false)"> ' +
            triangleButton +
        '</div>' +

        // This pane will be detached from here and re-attached to the document body.
        '<div class="md-datepicker-calendar-pane md-whiteframe-z1" id="{{::ctrl.calendarPaneId}}">' +
          '<div class="md-datepicker-input-mask">' +
            '<div class="md-datepicker-input-mask-opaque"></div>' +
          '</div>' +
          '<div class="md-datepicker-calendar">' +
            '<md-calendar role="dialog" aria-label="{{::ctrl.locale.msgCalendar}}" ' +
                'md-current-view="{{::ctrl.currentView}}" ' +
                'md-mode="{{::ctrl.mode}}" ' +
                'md-min-date="ctrl.minDate" ' +
                'md-max-date="ctrl.maxDate" ' +
                'md-date-filter="ctrl.dateFilter" ' +
                'md-month-filter="ctrl.monthFilter" ' +
                (ngModelOptions ? 'ng-model-options="' + ngModelOptions + '" ' : '') +
                'ng-model="ctrl.date" ng-if="ctrl.isCalendarOpen">' +
            '</md-calendar>' +
          '</div>' +
        '</div>';
      },
      require: ['ngModel', 'mdDatepicker', '?^mdInputContainer', '?^form'],
      scope: {
        minDate: '=mdMinDate',
        maxDate: '=mdMaxDate',
        placeholder: '@mdPlaceholder',
        currentView: '@mdCurrentView',
        mode: '@mdMode',
        dateFilter: '=mdDateFilter',
        monthFilter: '=mdMonthFilter',
        isOpen: '=?mdIsOpen',
        debounceInterval: '=mdDebounceInterval',
        dateLocale: '=mdDateLocale'
      },
      controller: DatePickerCtrl,
      controllerAs: 'ctrl',
      bindToController: true,
      link: function(scope, element, attr, controllers) {
        var ngModelCtrl = controllers[0];
        var mdDatePickerCtrl = controllers[1];
        var mdInputContainer = controllers[2];
        var parentForm = controllers[3];
        var mdNoAsterisk = $mdUtil.parseAttributeBoolean(attr.mdNoAsterisk);

        mdDatePickerCtrl.configureNgModel(ngModelCtrl, mdInputContainer, inputDirective);

        if (mdInputContainer) {
          // We need to move the spacer after the datepicker itself,
          // because md-input-container adds it after the
          // md-datepicker-input by default. The spacer gets wrapped in a
          // div, because it floats and gets aligned next to the datepicker.
          // There are easier ways of working around this with CSS (making the
          // datepicker 100% wide, change the `display` etc.), however they
          // break the alignment with any other form controls.
          var spacer = element[0].querySelector('.md-errors-spacer');

          if (spacer) {
            element.after(angular.element('<div>').append(spacer));
          }

          mdInputContainer.setHasPlaceholder(attr.mdPlaceholder);
          mdInputContainer.input = element;
          mdInputContainer.element
            .addClass(INPUT_CONTAINER_CLASS)
            .toggleClass(HAS_CALENDAR_ICON_CLASS,
              attr.mdHideIcons !== 'calendar' && attr.mdHideIcons !== 'all');

          if (!mdInputContainer.label) {
            $mdAria.expect(element, 'aria-label', attr.mdPlaceholder);
          } else if (!mdNoAsterisk) {
            attr.$observe('required', function(value) {
              mdInputContainer.label.toggleClass('md-required', !!value);
            });
          }

          scope.$watch(mdInputContainer.isErrorGetter || function() {
            return ngModelCtrl.$invalid && (ngModelCtrl.$touched ||
              (parentForm && parentForm.$submitted));
          }, mdInputContainer.setInvalid);
        } else if (parentForm) {
          // If invalid, highlights the input when the parent form is submitted.
          var parentSubmittedWatcher = scope.$watch(function() {
            return parentForm.$submitted;
          }, function(isSubmitted) {
            if (isSubmitted) {
              mdDatePickerCtrl.updateErrorState();
              parentSubmittedWatcher();
            }
          });
        }
      }
    };
  }

  /** Additional offset for the input's `size` attribute, which is updated based on its content. */
  var EXTRA_INPUT_SIZE = 3;

  /** Class applied to the container if the date is invalid. */
  var INVALID_CLASS = 'md-datepicker-invalid';

  /** Class applied to the datepicker when it's open. */
  var OPEN_CLASS = 'md-datepicker-open';

  /** Class applied to the md-input-container, if a datepicker is placed inside it */
  var INPUT_CONTAINER_CLASS = '_md-datepicker-floating-label';

  /** Class to be applied when the calendar icon is enabled. */
  var HAS_CALENDAR_ICON_CLASS = '_md-datepicker-has-calendar-icon';

  /** Class to be applied when the triangle icon is enabled. */
  var HAS_TRIANGLE_ICON_CLASS = '_md-datepicker-has-triangle-icon';

  /** Default time in ms to debounce input event by. */
  var DEFAULT_DEBOUNCE_INTERVAL = 500;

  /**
   * Height of the calendar pane used to check if the pane is going outside the boundary of
   * the viewport. See calendar.scss for how $md-calendar-height is computed; an extra 20px is
   * also added to space the pane away from the exact edge of the screen.
   *
   *  This is computed statically now, but can be changed to be measured if the circumstances
   *  of calendar sizing are changed.
   */
  var CALENDAR_PANE_HEIGHT = 368;

  /**
   * Width of the calendar pane used to check if the pane is going outside the boundary of
   * the viewport. See calendar.scss for how $md-calendar-width is computed; an extra 20px is
   * also added to space the pane away from the exact edge of the screen.
   *
   *  This is computed statically now, but can be changed to be measured if the circumstances
   *  of calendar sizing are changed.
   */
  var CALENDAR_PANE_WIDTH = 360;

  /** Used for checking whether the current user agent is on iOS or Android. */
  var IS_MOBILE_REGEX = /ipad|iphone|ipod|android/i;

  /**
   * Controller for md-datepicker.
   *
   * @ngInject @constructor
   */
  function DatePickerCtrl($scope, $element, $attrs, $window, $mdConstant, $mdTheming, $mdUtil,
                          $mdDateLocale, $$mdDateUtil, $$rAF, $filter, $timeout) {

    /** @final */
    this.$window = $window;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.$mdConstant = $mdConstant;

    /** @final */
    this.$mdUtil = $mdUtil;

    /** @final */
    this.$$rAF = $$rAF;

    /** @final */
    this.$mdDateLocale = $mdDateLocale;

    /** @final */
    this.$timeout = $timeout;

    /**
     * The root document element. This is used for attaching a top-level click handler to
     * close the calendar panel when a click outside said panel occurs. We use `documentElement`
     * instead of body because, when scrolling is disabled, some browsers consider the body element
     * to be completely off the screen and propagate events directly to the html element.
     * @type {!JQLite}
     */
    this.documentElement = angular.element(document.documentElement);

    /** @type {!ngModel.NgModelController} */
    this.ngModelCtrl = null;

    /** @type {HTMLInputElement} */
    this.inputElement = $element[0].querySelector('input');

    /**
     * @final
     * @type {!JQLite}
     */
    this.ngInputElement = angular.element(this.inputElement);

    /** @type {HTMLElement} */
    this.inputContainer = $element[0].querySelector('.md-datepicker-input-container');

    /** @type {HTMLElement} Floating calendar pane. */
    this.calendarPane = $element[0].querySelector('.md-datepicker-calendar-pane');

    /** @type {HTMLElement} Calendar icon button. */
    this.calendarButton = $element[0].querySelector('.md-datepicker-button');

    /**
     * Element covering everything but the input in the top of the floating calendar pane.
     * @type {!JQLite}
     */
    this.inputMask = angular.element($element[0].querySelector('.md-datepicker-input-mask-opaque'));

    /**
     * @final
     * @type {!JQLite}
     */
    this.$element = $element;

    /**
     * @final
     * @type {!angular.Attributes}
     */
    this.$attrs = $attrs;

    /**
     * @final
     * @type {!angular.Scope}
     */
    this.$scope = $scope;

    /** @type {Date} */
    this.date = null;

    /** @type {boolean} */
    this.isFocused = false;

    /** @type {boolean} */
    this.isDisabled = undefined;
    this.setDisabled($element[0].disabled || angular.isString($attrs.disabled));

    /** @type {boolean} Whether the date-picker's calendar pane is open. */
    this.isCalendarOpen = false;

    /** @type {boolean} Whether the calendar should open when the input is focused. */
    this.openOnFocus = $attrs.hasOwnProperty('mdOpenOnFocus');

    /** @type {Object} Instance of the mdInputContainer controller */
    this.mdInputContainer = null;

    /**
     * Element from which the calendar pane was opened. Keep track of this so that we can return
     * focus to it when the pane is closed.
     * @type {HTMLElement}
     */
    this.calendarPaneOpenedFrom = null;

    /** @type {String} Unique id for the calendar pane. */
    this.calendarPaneId = 'md-date-pane-' + $mdUtil.nextUid();

    /** Pre-bound click handler is saved so that the event listener can be removed. */
    this.bodyClickHandler = angular.bind(this, this.handleBodyClick);

    /**
     * Name of the event that will trigger a close. Necessary to sniff the browser, because
     * the resize event doesn't make sense on mobile and can have a negative impact since it
     * triggers whenever the browser zooms in on a focused input.
     */
    this.windowEventName = IS_MOBILE_REGEX.test(
      navigator.userAgent || navigator.vendor || window.opera
    ) ? 'orientationchange' : 'resize';

    /** Pre-bound close handler so that the event listener can be removed. */
    this.windowEventHandler = $mdUtil.debounce(angular.bind(this, this.closeCalendarPane), 100);

    /** Pre-bound handler for the window blur event. Allows for it to be removed later. */
    this.windowBlurHandler = angular.bind(this, this.handleWindowBlur);

    /** The built-in AngularJS date filter. */
    this.ngDateFilter = $filter('date');

    /** @type {Number} Extra margin for the left side of the floating calendar pane. */
    this.leftMargin = 20;

    /** @type {Number} Extra margin for the top of the floating calendar. Gets determined on the first open. */
    this.topMargin = null;

    // Unless the user specifies so, the datepicker should not be a tab stop.
    // This is necessary because ngAria might add a tabindex to anything with an ng-model
    // (based on whether or not the user has turned that particular feature on/off).
    if ($attrs.tabindex) {
      this.ngInputElement.attr('tabindex', $attrs.tabindex);
      $attrs.$set('tabindex', null);
    } else {
      $attrs.$set('tabindex', '-1');
    }

    $attrs.$set('aria-owns', this.calendarPaneId);

    $mdTheming($element);
    $mdTheming(angular.element(this.calendarPane));

    var self = this;

    $scope.$on('$destroy', function() {
      self.detachCalendarPane();
    });

    if ($attrs.mdIsOpen) {
      $scope.$watch('ctrl.isOpen', function(shouldBeOpen) {
        if (shouldBeOpen) {
          self.openCalendarPane({
            target: self.inputElement
          });
        } else {
          self.closeCalendarPane();
        }
      });
    }

    // For AngularJS 1.4 and older, where there are no lifecycle hooks but bindings are
    // pre-assigned, manually call the $onInit hook.
    if (angular.version.major === 1 && angular.version.minor <= 4) {
      this.$onInit();
    }
  }

  /**
   * AngularJS Lifecycle hook for newer AngularJS versions.
   * Bindings are not guaranteed to have been assigned in the controller, but they are in the
   * $onInit hook.
   */
  DatePickerCtrl.prototype.$onInit = function() {

    /**
     * Holds locale-specific formatters, parsers, labels etc. Allows
     * the user to override specific ones from the $mdDateLocale provider.
     * @type {!Object}
     */
    this.locale = this.dateLocale ? angular.extend({}, this.$mdDateLocale, this.dateLocale)
      : this.$mdDateLocale;

    this.installPropertyInterceptors();
    this.attachChangeListeners();
    this.attachInteractionListeners();
  };

  /**
   * Sets up the controller's reference to ngModelController and
   * applies AngularJS's `input[type="date"]` directive.
   * @param {!angular.NgModelController} ngModelCtrl Instance of the ngModel controller.
   * @param {Object} mdInputContainer Instance of the mdInputContainer controller.
   * @param {Object} inputDirective Config for AngularJS's `input` directive.
   */
  DatePickerCtrl.prototype.configureNgModel = function(ngModelCtrl, mdInputContainer, inputDirective) {
    this.ngModelCtrl = ngModelCtrl;
    this.mdInputContainer = mdInputContainer;

    // The input needs to be [type="date"] in order to be picked up by AngularJS.
    this.$attrs.$set('type', 'date');

    // Invoke the `input` directive link function, adding a stub for the element.
    // This allows us to re-use AngularJS's logic for setting the timezone via ng-model-options.
    // It works by calling the link function directly which then adds the proper `$parsers` and
    // `$formatters` to the ngModel controller.
    inputDirective[0].link.pre(this.$scope, {
      on: angular.noop,
      val: angular.noop,
      0: {}
    }, this.$attrs, [ngModelCtrl]);

    var self = this;

    // Responds to external changes to the model value.
    self.ngModelCtrl.$formatters.push(function(value) {
      var parsedValue = angular.isDefined(value) ? value : null;

      if (!(value instanceof Date)) {
        parsedValue = Date.parse(value);

        // `parsedValue` is the time since epoch if valid or `NaN` if invalid.
        if (!isNaN(parsedValue) && angular.isNumber(parsedValue)) {
          value = new Date(parsedValue);
        }

        if (value && !(value instanceof Date)) {
          throw Error(
            'The ng-model for md-datepicker must be a Date instance or a value ' +
              'that can be parsed into a date. Currently the model is of type: ' + typeof value
          );
        }
      }

      self.onExternalChange(value);

      return value;
    });

    // Responds to external error state changes (e.g. ng-required based on another input).
    ngModelCtrl.$viewChangeListeners.unshift(angular.bind(this, this.updateErrorState));

    // Forwards any events from the input to the root element. This is necessary to get `updateOn`
    // working for events that don't bubble (e.g. 'blur') since AngularJS binds the handlers to
    // the `<md-datepicker>`.
    var updateOn = self.$mdUtil.getModelOption(ngModelCtrl, 'updateOn');

    if (updateOn) {
      this.ngInputElement.on(
        updateOn,
        angular.bind(this.$element, this.$element.triggerHandler, updateOn)
      );
    }
  };

  /**
   * Attach event listeners for both the text input and the md-calendar.
   * Events are used instead of ng-model so that updates don't infinitely update the other
   * on a change. This should also be more performant than using a $watch.
   */
  DatePickerCtrl.prototype.attachChangeListeners = function() {
    var self = this;

    self.$scope.$on('md-calendar-change', function(event, date) {
      self.setModelValue(date);
      self.onExternalChange(date);
      self.closeCalendarPane();
    });

    self.ngInputElement.on('input', angular.bind(self, self.resizeInputElement));

    var debounceInterval = angular.isDefined(this.debounceInterval) ?
        this.debounceInterval : DEFAULT_DEBOUNCE_INTERVAL;
    self.ngInputElement.on('input', self.$mdUtil.debounce(self.handleInputEvent,
        debounceInterval, self));
  };

  /** Attach event listeners for user interaction. */
  DatePickerCtrl.prototype.attachInteractionListeners = function() {
    var self = this;
    var $scope = this.$scope;
    var keyCodes = this.$mdConstant.KEY_CODE;

    // Add event listener through angular so that we can triggerHandler in unit tests.
    self.ngInputElement.on('keydown', function(event) {
      if (event.altKey && event.keyCode === keyCodes.DOWN_ARROW) {
        self.openCalendarPane(event);
        $scope.$digest();
      }
    });

    if (self.openOnFocus) {
      self.ngInputElement.on('focus', angular.bind(self, self.openCalendarPane));
      self.ngInputElement.on('click', function(event) {
        event.stopPropagation();
      });
      self.ngInputElement.on('pointerdown',function(event) {
        if (event.target && event.target.setPointerCapture) {
          event.target.setPointerCapture(event.pointerId);
        }
      });

      angular.element(self.$window).on('blur', self.windowBlurHandler);

      $scope.$on('$destroy', function() {
        angular.element(self.$window).off('blur', self.windowBlurHandler);
      });
    }

    $scope.$on('md-calendar-close', function() {
      self.closeCalendarPane();
    });
  };

  /**
   * Capture properties set to the date-picker and imperatively handle internal changes.
   * This is done to avoid setting up additional $watches.
   */
  DatePickerCtrl.prototype.installPropertyInterceptors = function() {
    var self = this;

    if (this.$attrs.ngDisabled) {
      // The expression is to be evaluated against the directive element's scope and not
      // the directive's isolate scope.
      var scope = this.$scope.$parent;

      if (scope) {
        scope.$watch(this.$attrs.ngDisabled, function(isDisabled) {
          self.setDisabled(isDisabled);
        });
      }
    }

    Object.defineProperty(this, 'placeholder', {
      get: function() { return self.inputElement.placeholder; },
      set: function(value) { self.inputElement.placeholder = value || ''; }
    });
  };

  /**
   * Sets whether the date-picker is disabled.
   * @param {boolean} isDisabled
   */
  DatePickerCtrl.prototype.setDisabled = function(isDisabled) {
    this.isDisabled = isDisabled;
    this.inputElement.disabled = isDisabled;

    if (this.calendarButton) {
      this.calendarButton.disabled = isDisabled;
    }
  };

  /**
   * Sets the custom ngModel.$error flags to be consumed by ngMessages. Flags are:
   *   - mindate: whether the selected date is before the minimum date.
   *   - maxdate: whether the selected flag is after the maximum date.
   *   - filtered: whether the selected date is allowed by the custom filtering function.
   *   - valid: whether the entered text input is a valid date
   *
   * The 'required' flag is handled automatically by ngModel.
   *
   * @param {Date=} opt_date Date to check. If not given, defaults to the datepicker's model value.
   */
  DatePickerCtrl.prototype.updateErrorState = function(opt_date) {
    var date = opt_date || this.date;

    // Clear any existing errors to get rid of anything that's no longer relevant.
    this.clearErrorState();

    if (this.dateUtil.isValidDate(date)) {
      // Force all dates to midnight in order to ignore the time portion.
      date = this.dateUtil.createDateAtMidnight(date);

      if (this.dateUtil.isValidDate(this.minDate)) {
        var minDate = this.dateUtil.createDateAtMidnight(this.minDate);
        this.ngModelCtrl.$setValidity('mindate', date >= minDate);
      }

      if (this.dateUtil.isValidDate(this.maxDate)) {
        var maxDate = this.dateUtil.createDateAtMidnight(this.maxDate);
        this.ngModelCtrl.$setValidity('maxdate', date <= maxDate);
      }

      if (angular.isFunction(this.dateFilter)) {
        this.ngModelCtrl.$setValidity('filtered', this.dateFilter(date));
      }

      if (angular.isFunction(this.monthFilter)) {
        this.ngModelCtrl.$setValidity('filtered', this.monthFilter(date));
      }
    } else {
      // The date is seen as "not a valid date" if there is *something* set
      // (i.e.., not null or undefined), but that something isn't a valid date.
      this.ngModelCtrl.$setValidity('valid', date == null);
    }

    var input = this.inputElement.value;
    var parsedDate = this.locale.parseDate(input);

    if (!this.isInputValid(input, parsedDate) && this.ngModelCtrl.$valid) {
      this.ngModelCtrl.$setValidity('valid', date == null);
    }

    angular.element(this.inputContainer).toggleClass(INVALID_CLASS,
      this.ngModelCtrl.$invalid && (this.ngModelCtrl.$touched || this.ngModelCtrl.$submitted));
  };

  /**
   * Check to see if the input is valid, as the validation should fail if the model is invalid.
   *
   * @param {string} inputString
   * @param {Date} parsedDate
   * @return {boolean} Whether the input is valid
   */
  DatePickerCtrl.prototype.isInputValid = function (inputString, parsedDate) {
    return inputString === '' || (
      this.dateUtil.isValidDate(parsedDate) &&
      this.locale.isDateComplete(inputString) &&
      this.isDateEnabled(parsedDate)
    );
  };

  /** Clears any error flags set by `updateErrorState`. */
  DatePickerCtrl.prototype.clearErrorState = function() {
    this.inputContainer.classList.remove(INVALID_CLASS);
    ['mindate', 'maxdate', 'filtered', 'valid'].forEach(function(field) {
      this.ngModelCtrl.$setValidity(field, true);
    }, this);
  };

  /** Resizes the input element based on the size of its content. */
  DatePickerCtrl.prototype.resizeInputElement = function() {
    this.inputElement.size = this.inputElement.value.length + EXTRA_INPUT_SIZE;
  };

  /**
   * Sets the model value if the user input is a valid date.
   * Adds an invalid class to the input element if not.
   */
  DatePickerCtrl.prototype.handleInputEvent = function() {
    var inputString = this.inputElement.value;
    var parsedDate = inputString ? this.locale.parseDate(inputString) : null;
    this.dateUtil.setDateTimeToMidnight(parsedDate);

    // An input string is valid if it is either empty (representing no date)
    // or if it parses to a valid date that the user is allowed to select.
    var isValidInput = this.isInputValid(inputString, parsedDate);

    // The datepicker's model is only updated when there is a valid input.
    if (isValidInput) {
      this.setModelValue(parsedDate);
      this.date = parsedDate;
    }

    this.updateErrorState(parsedDate);
  };

  /**
   * Check whether date is in range and enabled
   * @param {Date=} opt_date
   * @return {boolean} Whether the date is enabled.
   */
  DatePickerCtrl.prototype.isDateEnabled = function(opt_date) {
    return this.dateUtil.isDateWithinRange(opt_date, this.minDate, this.maxDate) &&
          (!angular.isFunction(this.dateFilter) || this.dateFilter(opt_date)) &&
          (!angular.isFunction(this.monthFilter) || this.monthFilter(opt_date));
  };

  /** Position and attach the floating calendar to the document. */
  DatePickerCtrl.prototype.attachCalendarPane = function() {
    var calendarPane = this.calendarPane;
    var body = document.body;

    calendarPane.style.transform = '';
    this.$element.addClass(OPEN_CLASS);
    this.mdInputContainer && this.mdInputContainer.element.addClass(OPEN_CLASS);
    angular.element(body).addClass('md-datepicker-is-showing');

    var elementRect = this.inputContainer.getBoundingClientRect();
    var bodyRect = body.getBoundingClientRect();

    if (!this.topMargin || this.topMargin < 0) {
      this.topMargin =
        (this.inputMask.parent().prop('clientHeight')
          - this.ngInputElement.prop('clientHeight')) / 2;
    }

    // Check to see if the calendar pane would go off the screen. If so, adjust position
    // accordingly to keep it within the viewport.
    var paneTop = elementRect.top - bodyRect.top - this.topMargin;
    var paneLeft = elementRect.left - bodyRect.left - this.leftMargin;

    // If ng-material has disabled body scrolling (for example, if a dialog is open),
    // then it's possible that the already-scrolled body has a negative top/left. In this case,
    // we want to treat the "real" top as (0 - bodyRect.top). In a normal scrolling situation,
    // though, the top of the viewport should just be the body's scroll position.
    var viewportTop = (bodyRect.top < 0 && document.body.scrollTop === 0) ?
        -bodyRect.top :
        document.body.scrollTop;

    var viewportLeft = (bodyRect.left < 0 && document.body.scrollLeft === 0) ?
        -bodyRect.left :
        document.body.scrollLeft;

    var viewportBottom = viewportTop + this.$window.innerHeight;
    var viewportRight = viewportLeft + this.$window.innerWidth;

    // Creates an overlay with a hole the same size as element. We remove a pixel or two
    // on each end to make it overlap slightly. The overlay's background is added in
    // the theme in the form of a box-shadow with a huge spread.
    this.inputMask.css({
      position: 'absolute',
      left: this.leftMargin + 'px',
      top: this.topMargin + 'px',
      width: (elementRect.width - 1) + 'px',
      height: (elementRect.height - 2) + 'px'
    });

    // If the right edge of the pane would be off the screen and shifting it left by the
    // difference would not go past the left edge of the screen. If the calendar pane is too
    // big to fit on the screen at all, move it to the left of the screen and scale the entire
    // element down to fit.
    if (paneLeft + CALENDAR_PANE_WIDTH > viewportRight) {
      if (viewportRight - CALENDAR_PANE_WIDTH > 0) {
        paneLeft = viewportRight - CALENDAR_PANE_WIDTH;
      } else {
        paneLeft = viewportLeft;
        var scale = this.$window.innerWidth / CALENDAR_PANE_WIDTH;
        calendarPane.style.transform = 'scale(' + scale + ')';
      }

      calendarPane.classList.add('md-datepicker-pos-adjusted');
    }

    // If the bottom edge of the pane would be off the screen and shifting it up by the
    // difference would not go past the top edge of the screen.
    if (paneTop + CALENDAR_PANE_HEIGHT > viewportBottom &&
        viewportBottom - CALENDAR_PANE_HEIGHT > viewportTop) {
      paneTop = viewportBottom - CALENDAR_PANE_HEIGHT;
      calendarPane.classList.add('md-datepicker-pos-adjusted');
    }

    calendarPane.style.left = paneLeft + 'px';
    calendarPane.style.top = paneTop + 'px';
    document.body.appendChild(calendarPane);

    // Add CSS class after one frame to trigger open animation.
    this.$$rAF(function() {
      calendarPane.classList.add('md-pane-open');
    });
  };

  /** Detach the floating calendar pane from the document. */
  DatePickerCtrl.prototype.detachCalendarPane = function() {
    this.$element.removeClass(OPEN_CLASS);
    this.mdInputContainer && this.mdInputContainer.element.removeClass(OPEN_CLASS);
    angular.element(document.body).removeClass('md-datepicker-is-showing');
    this.calendarPane.classList.remove('md-pane-open');
    this.calendarPane.classList.remove('md-datepicker-pos-adjusted');

    if (this.isCalendarOpen) {
      this.$mdUtil.enableScrolling();
    }

    if (this.calendarPane.parentNode) {
      // Use native DOM removal because we do not want any of the
      // angular state of this element to be disposed.
      this.calendarPane.parentNode.removeChild(this.calendarPane);
    }
  };

  /**
   * Open the floating calendar pane.
   * @param {Event} event
   */
  DatePickerCtrl.prototype.openCalendarPane = function(event) {
    if (!this.isCalendarOpen && !this.isDisabled && !this.inputFocusedOnWindowBlur) {
      this.isCalendarOpen = this.isOpen = true;
      this.calendarPaneOpenedFrom = event.target;

      // Because the calendar pane is attached directly to the body, it is possible that the
      // rest of the component (input, etc) is in a different scrolling container, such as
      // an md-content. This means that, if the container is scrolled, the pane would remain
      // stationary. To remedy this, we disable scrolling while the calendar pane is open, which
      // also matches the native behavior for things like `<select>` on Mac and Windows.
      this.$mdUtil.disableScrollAround(this.calendarPane);

      this.attachCalendarPane();
      this.focusCalendar();
      this.evalAttr('ngFocus');

      // Attach click listener inside of a timeout because, if this open call was triggered by a
      // click, we don't want it to be immediately propagated up to the body and handled.
      var self = this;
      this.$mdUtil.nextTick(function() {
        // Use 'touchstart` in addition to click in order to work on iOS Safari, where click
        // events aren't propagated under most circumstances.
        // See http://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html
        self.documentElement.on('click touchstart', self.bodyClickHandler);
      }, false);

      window.addEventListener(this.windowEventName, this.windowEventHandler);
    } else if (this.inputFocusedOnWindowBlur) {
      this.resetInputFocused();
    }
  };

  /** Close the floating calendar pane. */
  DatePickerCtrl.prototype.closeCalendarPane = function() {
    if (this.isCalendarOpen) {
      var self = this;

      self.detachCalendarPane();
      self.ngModelCtrl.$setTouched();
      self.evalAttr('ngBlur');

      self.documentElement.off('click touchstart', self.bodyClickHandler);
      window.removeEventListener(self.windowEventName, self.windowEventHandler);

      self.calendarPaneOpenedFrom.focus();
      self.calendarPaneOpenedFrom = null;

      if (self.openOnFocus) {
        // Ensures that all focus events have fired before resetting
        // the calendar. Prevents the calendar from reopening immediately
        // in IE when md-open-on-focus is set. Also it needs to trigger
        // a digest, in order to prevent issues where the calendar wasn't
        // showing up on the next open.
        self.$timeout(reset);
      } else {
        reset();
      }
    }

    function reset(){
      self.isCalendarOpen = self.isOpen = false;
    }
  };

  /** Gets the controller instance for the calendar in the floating pane. */
  DatePickerCtrl.prototype.getCalendarCtrl = function() {
    return angular.element(this.calendarPane.querySelector('md-calendar')).controller('mdCalendar');
  };

  /** Focus the calendar in the floating pane. */
  DatePickerCtrl.prototype.focusCalendar = function() {
    // Use a timeout in order to allow the calendar to be rendered, as it is gated behind an ng-if.
    var self = this;
    this.$mdUtil.nextTick(function() {
      self.getCalendarCtrl().focusDate();
    }, false);
  };

  /**
   * Sets whether the input is currently focused.
   * @param {boolean} isFocused
   */
  DatePickerCtrl.prototype.setFocused = function(isFocused) {
    if (!isFocused) {
      this.ngModelCtrl.$setTouched();
    }

    // The ng* expressions shouldn't be evaluated when mdOpenOnFocus is on,
    // because they also get called when the calendar is opened/closed.
    if (!this.openOnFocus) {
      this.evalAttr(isFocused ? 'ngFocus' : 'ngBlur');
    }

    this.isFocused = isFocused;
  };

  /**
   * Handles a click on the document body when the floating calendar pane is open.
   * Closes the floating calendar pane if the click is not inside of it.
   * @param {MouseEvent} event
   */
  DatePickerCtrl.prototype.handleBodyClick = function(event) {
    if (this.isCalendarOpen) {
      var isInCalendar = this.$mdUtil.getClosest(event.target, 'md-calendar');

      if (!isInCalendar) {
        this.closeCalendarPane();
      }

      this.$scope.$digest();
    }
  };

  /**
   * Handles the event when the user navigates away from the current tab. Keeps track of
   * whether the input was focused when the event happened, in order to prevent the calendar
   * from re-opening.
   */
  DatePickerCtrl.prototype.handleWindowBlur = function() {
    this.inputFocusedOnWindowBlur = document.activeElement === this.inputElement;
  };

  /**
   * Reset the flag inputFocusedOnWindowBlur to default state, to permit user to open calendar
   * again when he back to tab with calendar focused.
   */
  DatePickerCtrl.prototype.resetInputFocused = function() {
    this.inputFocusedOnWindowBlur = false;
  };

  /**
   * Evaluates an attribute expression against the parent scope.
   * @param {String} attr Name of the attribute to be evaluated.
   */
  DatePickerCtrl.prototype.evalAttr = function(attr) {
    if (this.$attrs[attr]) {
      this.$scope.$parent.$eval(this.$attrs[attr]);
    }
  };

  /**
   * Sets the ng-model value by first converting the date object into a string. Converting it
   * is necessary, in order to pass AngularJS's `input[type="date"]` validations. AngularJS turns
   * the value into a Date object afterwards, before setting it on the model.
   * @param {Date=} value Date to be set as the model value.
   */
  DatePickerCtrl.prototype.setModelValue = function(value) {
    var timezone = this.$mdUtil.getModelOption(this.ngModelCtrl, 'timezone');
    this.ngModelCtrl.$setViewValue(this.ngDateFilter(value, 'yyyy-MM-dd', timezone), 'default');
  };

  /**
   * Updates the datepicker when a model change occurred externally.
   * @param {Date=} value Value that was set to the model.
   */
  DatePickerCtrl.prototype.onExternalChange = function(value) {
    var self = this;
    var timezone = this.$mdUtil.getModelOption(this.ngModelCtrl, 'timezone');

    if (this.dateUtil.isValidDate(value)) {
      this.date = this.dateUtil.removeLocalTzAndReparseDate(value);
    } else {
      this.date = value;
    }
    this.inputElement.value = this.locale.formatDate(value, timezone);
    this.mdInputContainer && this.mdInputContainer.setHasValue(!!value);
    this.resizeInputElement();
    // This is often called from the $formatters section of the $validators pipeline.
    // In that case, we need to delay to let $render and $validate run, so that the checks for
    // error state are accurate.
    this.$mdUtil.nextTick(function() {self.updateErrorState();}, false, self.$scope);
  };
})();

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.dialog
 */
MdDialogDirective.$inject = ["$$rAF", "$mdTheming", "$mdDialog"];
MdDialogProvider.$inject = ["$$interimElementProvider"];
angular
  .module('material.components.dialog', [
    'material.core',
    'material.components.backdrop'
  ])
  .directive('mdDialog', MdDialogDirective)
  .provider('$mdDialog', MdDialogProvider);

/**
 * @ngdoc directive
 * @name mdDialog
 * @module material.components.dialog
 *
 * @restrict E
 *
 * @description
 * `<md-dialog>` - The dialog's template must be inside this element.
 *
 * Inside, use an `<md-dialog-content>` element for the dialog's content, and use
 * an `<md-dialog-actions>` element for the dialog's actions.
 *
 * ## CSS
 * - `.md-dialog-content` - class that sets the padding on the content as the spec file
 *
 * ## Notes
 * - If you specify an `id` for the `<md-dialog>`, the `<md-dialog-content>` will have the same `id`
 * prefixed with `dialogContent_`.
 *
 * @usage
 * ### Dialog template
 * <hljs lang="html">
 * <md-dialog aria-label="List dialog">
 *   <md-dialog-content>
 *     <md-list>
 *       <md-list-item ng-repeat="item in items">
 *         <p>Number {{item}}</p>
 *       </md-list-item>
 *     </md-list>
 *   </md-dialog-content>
 *   <md-dialog-actions>
 *     <md-button ng-click="closeDialog()" class="md-primary">Close Dialog</md-button>
 *   </md-dialog-actions>
 * </md-dialog>
 * </hljs>
 */
function MdDialogDirective($$rAF, $mdTheming, $mdDialog) {
  return {
    restrict: 'E',
    link: function(scope, element) {
      element.addClass('_md');     // private md component indicator for styling

      $mdTheming(element);
      $$rAF(function() {
        var images;
        var content = element[0].querySelector('md-dialog-content');

        if (content) {
          images = content.getElementsByTagName('img');
          addOverflowClass();
          // delayed image loading may impact scroll height, check after images are loaded
          angular.element(images).on('load', addOverflowClass);
        }

        scope.$on('$destroy', function() {
          $mdDialog.destroy(element);
        });

        /**
         *
         */
        function addOverflowClass() {
          element.toggleClass('md-content-overflow', content.scrollHeight > content.clientHeight);
        }


      });
    }
  };
}

/**
 * @ngdoc service
 * @name $mdDialog
 * @module material.components.dialog
 *
 * @description
 * `$mdDialog` opens a dialog over the app to inform users about critical information or require
 *  them to make decisions. There are two approaches for setup: a simple promise API
 *  and regular object syntax.
 *
 * ## Restrictions
 *
 * - The dialog is always given an isolate scope.
 * - The dialog's template must have an outer `<md-dialog>` element.
 *   Inside, use an `<md-dialog-content>` element for the dialog's content, and use
 *   an `<md-dialog-actions>` element for the dialog's actions.
 * - Dialogs must cover the entire application to keep interactions inside of them.
 * Use the `parent` option to change where dialogs are appended.
 *
 * ## Sizing
 * - Complex dialogs can be sized with `flex="percentage"`, i.e. `flex="66"`.
 * - Default max-width is 80% of the `rootElement` or `parent`.
 *
 * ## CSS
 * - `.md-dialog-content` - class that sets the padding on the content as the spec file
 *
 * @usage
 * <hljs lang="html">
 * <div  ng-app="demoApp" ng-controller="EmployeeController">
 *   <div>
 *     <md-button ng-click="showAlert()" class="md-raised md-warn">
 *       Employee Alert!
 *       </md-button>
 *   </div>
 *   <div>
 *     <md-button ng-click="showDialog($event)" class="md-raised">
 *       Custom Dialog
 *       </md-button>
 *   </div>
 *   <div>
 *     <md-button ng-click="closeAlert()" ng-disabled="!hasAlert()" class="md-raised">
 *       Close Alert
 *     </md-button>
 *   </div>
 *   <div>
 *     <md-button ng-click="showGreeting($event)" class="md-raised md-primary" >
 *       Greet Employee
 *       </md-button>
 *   </div>
 * </div>
 * </hljs>
 *
 * ### JavaScript: object syntax
 * <hljs lang="js">
 * (function(angular, undefined){
 *   "use strict";
 *
 *   angular
 *    .module('demoApp', ['ngMaterial'])
 *    .controller('AppCtrl', AppController);
 *
 *   function AppController($scope, $mdDialog) {
 *     var alert;
 *     $scope.showAlert = showAlert;
 *     $scope.showDialog = showDialog;
 *     $scope.items = [1, 2, 3];
 *
 *     // Internal method
 *     function showAlert() {
 *       alert = $mdDialog.alert({
 *         title: 'Attention',
 *         textContent: 'This is an example of how easy dialogs can be!',
 *         ok: 'Close'
 *       });
 *
 *       $mdDialog
 *         .show( alert )
 *         .finally(function() {
 *           alert = undefined;
 *         });
 *     }
 *
 *     function showDialog($event) {
 *        var parentEl = angular.element(document.body);
 *        $mdDialog.show({
 *          parent: parentEl,
 *          targetEvent: $event,
 *          template:
 *            '<md-dialog aria-label="List dialog">' +
 *            '  <md-dialog-content>'+
 *            '    <md-list>'+
 *            '      <md-list-item ng-repeat="item in items">'+
 *            '       <p>Number {{item}}</p>' +
 *            '      </md-item>'+
 *            '    </md-list>'+
 *            '  </md-dialog-content>' +
 *            '  <md-dialog-actions>' +
 *            '    <md-button ng-click="closeDialog()" class="md-primary">' +
 *            '      Close Dialog' +
 *            '    </md-button>' +
 *            '  </md-dialog-actions>' +
 *            '</md-dialog>',
 *          locals: {
 *            items: $scope.items
 *          },
 *          controller: DialogController
 *       });
 *       function DialogController($scope, $mdDialog, items) {
 *         $scope.items = items;
 *         $scope.closeDialog = function() {
 *           $mdDialog.hide();
 *         }
 *       }
 *     }
 *   }
 * })(angular);
 * </hljs>
 *
 * ### Multiple Dialogs
 * Using the `multiple` option for the `$mdDialog` service allows developers to show multiple dialogs
 * at the same time.
 *
 * <hljs lang="js">
 *   // From plain options
 *   $mdDialog.show({
 *     multiple: true
 *   });
 *
 *   // From a dialog preset
 *   $mdDialog.show(
 *     $mdDialog
 *       .alert()
 *       .multiple(true)
 *   );
 *
 * </hljs>
 *
 * ### Pre-Rendered Dialogs
 * By using the `contentElement` option, it is possible to use an already existing element in the DOM.
 *
 * > Pre-rendered dialogs will be not linked to any scope and will not instantiate any new controller.<br/>
 * > You can manually link the elements to a scope or instantiate a controller from the template (`ng-controller`)
 *
 * <hljs lang="js">
 *   $scope.showPrerenderedDialog = function() {
 *     $mdDialog.show({
 *       contentElement: '#myStaticDialog',
 *       parent: angular.element(document.body)
 *     });
 *   };
 * </hljs>
 *
 * When using a string as value, `$mdDialog` will automatically query the DOM for the specified CSS selector.
 *
 * <hljs lang="html">
 *   <div style="visibility: hidden">
 *     <div class="md-dialog-container" id="myStaticDialog">
 *       <md-dialog>
 *         This is a pre-rendered dialog.
 *       </md-dialog>
 *     </div>
 *   </div>
 * </hljs>
 *
 * **Notice**: It is important, to use the `.md-dialog-container` as the content element, otherwise the dialog
 * will not show up.
 *
 * It also possible to use a DOM element for the `contentElement` option.
 * - `contentElement: document.querySelector('#myStaticDialog')`
 * - `contentElement: angular.element(TEMPLATE)`
 *
 * When using a `template` as content element, it will be not compiled upon open.
 * This allows you to compile the element yourself and use it each time the dialog opens.
 *
 * ### Custom Presets
 * Developers are also able to create their own preset, which can be easily used without repeating
 * their options each time.
 *
 * <hljs lang="js">
 *   $mdDialogProvider.addPreset('testPreset', {
 *     options: function() {
 *       return {
 *         template:
 *           '<md-dialog>' +
 *             'This is a custom preset' +
 *           '</md-dialog>',
 *         controllerAs: 'dialog',
 *         bindToController: true,
 *         clickOutsideToClose: true,
 *         escapeToClose: true
 *       };
 *     }
 *   });
 * </hljs>
 *
 * After you created your preset at config phase, you can easily access it.
 *
 * <hljs lang="js">
 *   $mdDialog.show(
 *     $mdDialog.testPreset()
 *   );
 * </hljs>
 *
 * ### JavaScript: promise API syntax, custom dialog template
 * <hljs lang="js">
 * (function(angular, undefined){
 *   "use strict";
 *
 *   angular
 *     .module('demoApp', ['ngMaterial'])
 *     .controller('EmployeeController', EmployeeEditor)
 *     .controller('GreetingController', GreetingController);
 *
 *   // Fictitious Employee Editor to show how to use simple and complex dialogs.
 *
 *   function EmployeeEditor($scope, $mdDialog) {
 *     var alert;
 *
 *     $scope.showAlert = showAlert;
 *     $scope.closeAlert = closeAlert;
 *     $scope.showGreeting = showCustomGreeting;
 *
 *     $scope.hasAlert = function() { return !!alert };
 *     $scope.userName = $scope.userName || 'Bobby';
 *
 *     // Dialog #1 - Show simple alert dialog and cache
 *     // reference to dialog instance
 *
 *     function showAlert() {
 *       alert = $mdDialog.alert()
 *         .title('Attention, ' + $scope.userName)
 *         .textContent('This is an example of how easy dialogs can be!')
 *         .ok('Close');
 *
 *       $mdDialog
 *           .show( alert )
 *           .finally(function() {
 *             alert = undefined;
 *           });
 *     }
 *
 *     // Close the specified dialog instance and resolve with 'finished' flag
 *     // Normally this is not needed, just use '$mdDialog.hide()' to close
 *     // the most recent dialog popup.
 *
 *     function closeAlert() {
 *       $mdDialog.hide( alert, "finished" );
 *       alert = undefined;
 *     }
 *
 *     // Dialog #2 - Demonstrate more complex dialogs construction and popup.
 *
 *     function showCustomGreeting($event) {
 *         $mdDialog.show({
 *           targetEvent: $event,
 *           template:
 *             '<md-dialog>' +
 *
 *             '  <md-dialog-content>Hello {{ employee }}!</md-dialog-content>' +
 *
 *             '  <md-dialog-actions>' +
 *             '    <md-button ng-click="closeDialog()" class="md-primary">' +
 *             '      Close Greeting' +
 *             '    </md-button>' +
 *             '  </md-dialog-actions>' +
 *             '</md-dialog>',
 *           controller: 'GreetingController',
 *           onComplete: afterShowAnimation,
 *           locals: { employee: $scope.userName }
 *         });
 *
 *         // When the 'enter' animation finishes...
 *
 *         function afterShowAnimation(scope, element, options) {
 *            // post-show code here: DOM element focus, etc.
 *         }
 *     }
 *
 *     // Dialog #3 - Demonstrate use of ControllerAs and passing $scope to dialog
 *     //             Here we used ng-controller="GreetingController as vm" and
 *     //             $scope.vm === <controller instance>
 *
 *     function showCustomGreeting() {
 *
 *        $mdDialog.show({
 *           clickOutsideToClose: true,
 *
 *           scope: $scope,        // use parent scope in template
 *           preserveScope: true,  // do not forget this if use parent scope

 *           // Since GreetingController is instantiated with ControllerAs syntax
 *           // AND we are passing the parent '$scope' to the dialog, we MUST
 *           // use 'vm.<xxx>' in the template markup
 *
 *           template: '<md-dialog>' +
 *                     '  <md-dialog-content>' +
 *                     '     Hi There {{vm.employee}}' +
 *                     '  </md-dialog-content>' +
 *                     '</md-dialog>',
 *
 *           controller: function DialogController($scope, $mdDialog) {
 *             $scope.closeDialog = function() {
 *               $mdDialog.hide();
 *             }
 *           }
 *        });
 *     }
 *
 *   }
 *
 *   // Greeting controller used with the more complex 'showCustomGreeting()' custom dialog
 *
 *   function GreetingController($scope, $mdDialog, employee) {
 *     // Assigned from construction <code>locals</code> options...
 *     $scope.employee = employee;
 *
 *     $scope.closeDialog = function() {
 *       // Easily hides most recent dialog shown...
 *       // no specific instance reference is needed.
 *       $mdDialog.hide();
 *     };
 *   }
 *
 * })(angular);
 * </hljs>
 */

/**
 * @ngdoc method
 * @name $mdDialog#alert
 *
 * @description
 * Builds a preconfigured dialog with the specified message.
 *
 * @returns {obj} an `$mdDialogPreset` with the chainable configuration methods:
 *
 * - $mdDialogPreset#title(string) - Sets the alert title.
 * - $mdDialogPreset#textContent(string) - Sets the alert message.
 * - $mdDialogPreset#htmlContent(string) - Sets the alert message as HTML. Requires ngSanitize
 *     module to be loaded. HTML is not run through Angular's compiler.
 * - $mdDialogPreset#ok(string) - Sets the alert "Okay" button text.
 * - $mdDialogPreset#theme(string) - Sets the theme of the alert dialog.
 * - $mdDialogPreset#targetEvent(DOMClickEvent=) - A click's event object. When passed in as an option,
 *     the location of the click will be used as the starting point for the opening animation
 *     of the the dialog.
 *
 */

/**
 * @ngdoc method
 * @name $mdDialog#confirm
 *
 * @description
 * Builds a preconfigured dialog with the specified message. You can call show and the promise
 * returned will be resolved if the user clicks the confirm action on the dialog. The promise will
 * be rejected if the user clicks the cancel action or dismisses the dialog.
 *
 * @returns {obj} an `$mdDialogPreset` with the chainable configuration methods:
 *
 * Additionally, it supports the following methods:
 *
 * - $mdDialogPreset#title(string) - Sets the confirm title.
 * - $mdDialogPreset#textContent(string) - Sets the confirm message.
 * - $mdDialogPreset#htmlContent(string) - Sets the confirm message as HTML. Requires ngSanitize
 *     module to be loaded. HTML is not run through Angular's compiler.
 * - $mdDialogPreset#ok(string) - Sets the confirm "Okay" button text.
 * - $mdDialogPreset#cancel(string) - Sets the confirm "Cancel" button text.
 * - $mdDialogPreset#theme(string) - Sets the theme of the confirm dialog.
 * - $mdDialogPreset#targetEvent(DOMClickEvent=) - A click's event object. When passed in as an option,
 *     the location of the click will be used as the starting point for the opening animation
 *     of the the dialog.
 *
 */

/**
 * @ngdoc method
 * @name $mdDialog#prompt
 *
 * @description
 * Builds a preconfigured dialog with the specified message and input box. You can call show and the
 * promise returned will be resolved, if the user clicks the prompt action on the dialog, passing
 * the input value as the first argument. The promise will be rejected if the user clicks the cancel
 * action or dismisses the dialog.
 *
 * @returns {obj} an `$mdDialogPreset` with the chainable configuration methods:
 *
 * Additionally, it supports the following methods:
 *
 * - $mdDialogPreset#title(string) - Sets the prompt title.
 * - $mdDialogPreset#textContent(string) - Sets the prompt message.
 * - $mdDialogPreset#htmlContent(string) - Sets the prompt message as HTML. Requires ngSanitize
 *     module to be loaded. HTML is not run through Angular's compiler.
 * - $mdDialogPreset#placeholder(string) - Sets the placeholder text for the input.
 * - $mdDialogPreset#required(boolean) - Sets the input required value.
 * - $mdDialogPreset#initialValue(string) - Sets the initial value for the prompt input.
 * - $mdDialogPreset#ok(string) - Sets the prompt "Okay" button text.
 * - $mdDialogPreset#cancel(string) - Sets the prompt "Cancel" button text.
 * - $mdDialogPreset#theme(string) - Sets the theme of the prompt dialog.
 * - $mdDialogPreset#targetEvent(DOMClickEvent=) - A click's event object. When passed in as an option,
 *     the location of the click will be used as the starting point for the opening animation
 *     of the the dialog.
 *
 */

/**
 * @ngdoc method
 * @name $mdDialog#show
 *
 * @description
 * Show a dialog with the specified options.
 *
 * @param {object} optionsOrPreset Either provide an `$mdDialogPreset` returned from `alert()`, and
 * `confirm()`, or an options object with the following properties:
 *   - `templateUrl` - `{string=}`: The url of a template that will be used as the content
 *   of the dialog.
 *   - `template` - `{string=}`: HTML template to show in the dialog. This **must** be trusted HTML
 *      with respect to Angular's [$sce service](https://docs.angularjs.org/api/ng/service/$sce).
 *      This template should **never** be constructed with any kind of user input or user data.
 *   - `contentElement` - `{string|Element}`: Instead of using a template, which will be compiled each time a
 *     dialog opens, you can also use a DOM element.<br/>
 *     * When specifying an element, which is present on the DOM, `$mdDialog` will temporary fetch the element into
 *       the dialog and restores it at the old DOM position upon close.
 *     * When specifying a string, the string be used as a CSS selector, to lookup for the element in the DOM.
 *   - `autoWrap` - `{boolean=}`: Whether or not to automatically wrap the template with a
 *     `<md-dialog>` tag if one is not provided. Defaults to true. Can be disabled if you provide a
 *     custom dialog directive.
 *   - `targetEvent` - `{DOMClickEvent=}`: A click's event object. When passed in as an option,
 *     the location of the click will be used as the starting point for the opening animation
 *     of the the dialog.
 *   - `openFrom` - `{string|Element|object}`: The query selector, DOM element or the Rect object
 *     that is used to determine the bounds (top, left, height, width) from which the Dialog will
 *     originate.
 *   - `closeTo` - `{string|Element|object}`: The query selector, DOM element or the Rect object
 *     that is used to determine the bounds (top, left, height, width) to which the Dialog will
 *     target.
 *   - `scope` - `{object=}`: the scope to link the template / controller to. If none is specified,
 *     it will create a new isolate scope.
 *     This scope will be destroyed when the dialog is removed unless `preserveScope` is set to true.
 *   - `preserveScope` - `{boolean=}`: whether to preserve the scope when the element is removed. Default is false
 *   - `disableParentScroll` - `{boolean=}`: Whether to disable scrolling while the dialog is open.
 *     Default true.
 *   - `hasBackdrop` - `{boolean=}`: Whether there should be an opaque backdrop behind the dialog.
 *     Default true.
 *   - `clickOutsideToClose` - `{boolean=}`: Whether the user can click outside the dialog to
 *     close it. Default false.
 *   - `escapeToClose` - `{boolean=}`: Whether the user can press escape to close the dialog.
 *     Default true.
 *   - `focusOnOpen` - `{boolean=}`: An option to override focus behavior on open. Only disable if
 *     focusing some other way, as focus management is required for dialogs to be accessible.
 *     Defaults to true.
 *   - `controller` - `{function|string=}`: The controller to associate with the dialog. The controller
 *     will be injected with the local `$mdDialog`, which passes along a scope for the dialog.
 *   - `locals` - `{object=}`: An object containing key/value pairs. The keys will be used as names
 *     of values to inject into the controller. For example, `locals: {three: 3}` would inject
 *     `three` into the controller, with the value 3. If `bindToController` is true, they will be
 *     copied to the controller instead.
 *   - `bindToController` - `bool`: bind the locals to the controller, instead of passing them in.
 *   - `resolve` - `{function=}`: Similar to locals, except it takes as values functions that return promises, and the
 *      dialog will not open until all of the promises resolve.
 *   - `controllerAs` - `{string=}`: An alias to assign the controller to on the scope.
 *   - `parent` - `{element=}`: The element to append the dialog to. Defaults to appending
 *     to the root element of the application.
 *   - `onShowing` - `function(scope, element)`: Callback function used to announce the show() action is
 *     starting.
 *   - `onComplete` - `function(scope, element)`: Callback function used to announce when the show() action is
 *     finished.
 *   - `onRemoving` - `function(element, removePromise)`: Callback function used to announce the
 *      close/hide() action is starting. This allows developers to run custom animations
 *      in parallel with the close animations.
 *   - `fullscreen` `{boolean=}`: An option to toggle whether the dialog should show in fullscreen
 *      or not. Defaults to `false`.
 *   - `multiple` `{boolean=}`: An option to allow this dialog to display over one that's currently open.
 * @returns {promise} A promise that can be resolved with `$mdDialog.hide()` or
 * rejected with `$mdDialog.cancel()`.
 */

/**
 * @ngdoc method
 * @name $mdDialog#hide
 *
 * @description
 * Hide an existing dialog and resolve the promise returned from `$mdDialog.show()`.
 *
 * @param {*=} response An argument for the resolved promise.
 *
 * @returns {promise} A promise that is resolved when the dialog has been closed.
 */

/**
 * @ngdoc method
 * @name $mdDialog#cancel
 *
 * @description
 * Hide an existing dialog and reject the promise returned from `$mdDialog.show()`.
 *
 * @param {*=} response An argument for the rejected promise.
 *
 * @returns {promise} A promise that is resolved when the dialog has been closed.
 */

function MdDialogProvider($$interimElementProvider) {
  // Elements to capture and redirect focus when the user presses tab at the dialog boundary.
  MdDialogController.$inject = ["$mdDialog", "$mdConstant"];
  dialogDefaultOptions.$inject = ["$mdDialog", "$mdAria", "$mdUtil", "$mdConstant", "$animate", "$document", "$window", "$rootElement", "$log", "$injector", "$mdTheming", "$interpolate", "$mdInteraction"];
  var topFocusTrap, bottomFocusTrap;

  return $$interimElementProvider('$mdDialog')
    .setDefaults({
      methods: ['disableParentScroll', 'hasBackdrop', 'clickOutsideToClose', 'escapeToClose',
          'targetEvent', 'closeTo', 'openFrom', 'parent', 'fullscreen', 'multiple'],
      options: dialogDefaultOptions
    })
    .addPreset('alert', {
      methods: ['title', 'htmlContent', 'textContent', 'content', 'ariaLabel', 'ok', 'theme',
          'css'],
      options: advancedDialogOptions
    })
    .addPreset('confirm', {
      methods: ['title', 'htmlContent', 'textContent', 'content', 'ariaLabel', 'ok', 'cancel',
          'theme', 'css'],
      options: advancedDialogOptions
    })
    .addPreset('prompt', {
      methods: ['title', 'htmlContent', 'textContent', 'initialValue', 'content', 'placeholder', 'ariaLabel',
          'ok', 'cancel', 'theme', 'css', 'required'],
      options: advancedDialogOptions
    });

  /* @ngInject */
  function advancedDialogOptions() {
    return {
      template: [
        '<md-dialog md-theme="{{ dialog.theme || dialog.defaultTheme }}" aria-label="{{ dialog.ariaLabel }}" ng-class="dialog.css">',
        '  <md-dialog-content class="md-dialog-content" role="document" tabIndex="-1">',
        '    <h2 class="md-title">{{ dialog.title }}</h2>',
        '    <div ng-if="::dialog.mdHtmlContent" class="md-dialog-content-body" ',
        '        ng-bind-html="::dialog.mdHtmlContent"></div>',
        '    <div ng-if="::!dialog.mdHtmlContent" class="md-dialog-content-body">',
        '      <p>{{::dialog.mdTextContent}}</p>',
        '    </div>',
        '    <md-input-container md-no-float ng-if="::dialog.$type == \'prompt\'" class="md-prompt-input-container">',
        '      <input ng-keypress="dialog.keypress($event)" md-autofocus ng-model="dialog.result" ' +
        '             placeholder="{{::dialog.placeholder}}" ng-required="dialog.required">',
        '    </md-input-container>',
        '  </md-dialog-content>',
        '  <md-dialog-actions>',
        '    <md-button ng-if="dialog.$type === \'confirm\' || dialog.$type === \'prompt\'"' +
        '               ng-click="dialog.abort()" class="md-primary md-cancel-button">',
        '      {{ dialog.cancel }}',
        '    </md-button>',
        '    <md-button ng-click="dialog.hide()" class="md-primary md-confirm-button" md-autofocus="dialog.$type===\'alert\'"' +
        '               ng-disabled="dialog.required && !dialog.result">',
        '      {{ dialog.ok }}',
        '    </md-button>',
        '  </md-dialog-actions>',
        '</md-dialog>'
      ].join('').replace(/\s\s+/g, ''),
      controller: MdDialogController,
      controllerAs: 'dialog',
      bindToController: true,
    };
  }

  /**
   * Controller for the md-dialog interim elements
   * @ngInject
   */
  function MdDialogController($mdDialog, $mdConstant) {
    // For compatibility with AngularJS 1.6+, we should always use the $onInit hook in
    // interimElements. The $mdCompiler simulates the $onInit hook for all versions.
    this.$onInit = function() {
      var isPrompt = this.$type == 'prompt';

      if (isPrompt && this.initialValue) {
        this.result = this.initialValue;
      }

      this.hide = function() {
        $mdDialog.hide(isPrompt ? this.result : true);
      };
      this.abort = function() {
        $mdDialog.cancel();
      };
      this.keypress = function($event) {
        var invalidPrompt = isPrompt && this.required && !angular.isDefined(this.result);

        if ($event.keyCode === $mdConstant.KEY_CODE.ENTER && !invalidPrompt) {
          $mdDialog.hide(this.result);
        }
      };
    };
  }

  /* @ngInject */
  function dialogDefaultOptions($mdDialog, $mdAria, $mdUtil, $mdConstant, $animate, $document, $window, $rootElement,
                                $log, $injector, $mdTheming, $interpolate, $mdInteraction) {

    return {
      hasBackdrop: true,
      isolateScope: true,
      onCompiling: beforeCompile,
      onShow: onShow,
      onShowing: beforeShow,
      onRemove: onRemove,
      clickOutsideToClose: false,
      escapeToClose: true,
      targetEvent: null,
      closeTo: null,
      openFrom: null,
      focusOnOpen: true,
      disableParentScroll: true,
      autoWrap: true,
      fullscreen: false,
      transformTemplate: function(template, options) {
        // Make the dialog container focusable, because otherwise the focus will be always redirected to
        // an element outside of the container, and the focus trap won't work probably..
        // Also the tabindex is needed for the `escapeToClose` functionality, because
        // the keyDown event can't be triggered when the focus is outside of the container.
        var startSymbol = $interpolate.startSymbol();
        var endSymbol = $interpolate.endSymbol();
        var theme = startSymbol + (options.themeWatch ? '' : '::') + 'theme' + endSymbol;
        var themeAttr = (options.hasTheme) ? 'md-theme="'+theme+'"': '';
        return '<div class="md-dialog-container" tabindex="-1" ' + themeAttr + '>' + validatedTemplate(template) + '</div>';

        /**
         * The specified template should contain a <md-dialog> wrapper element....
         */
        function validatedTemplate(template) {
          if (options.autoWrap && !/<\/md-dialog>/g.test(template)) {
            return '<md-dialog>' + (template || '') + '</md-dialog>';
          } else {
            return template || '';
          }
        }
      }
    };

    function beforeCompile(options) {
      // Automatically apply the theme, if the user didn't specify a theme explicitly.
      // Those option changes need to be done, before the compilation has started, because otherwise
      // the option changes will be not available in the $mdCompilers locales.
      options.defaultTheme = $mdTheming.defaultTheme();

      detectTheming(options);
    }

    function beforeShow(scope, element, options, controller) {

      if (controller) {
        var mdHtmlContent = controller.htmlContent || options.htmlContent || '';
        var mdTextContent = controller.textContent || options.textContent ||
            controller.content || options.content || '';

        if (mdHtmlContent && !$injector.has('$sanitize')) {
          throw Error('The ngSanitize module must be loaded in order to use htmlContent.');
        }

        if (mdHtmlContent && mdTextContent) {
          throw Error('md-dialog cannot have both `htmlContent` and `textContent`');
        }

        // Only assign the content if nothing throws, otherwise it'll still be compiled.
        controller.mdHtmlContent = mdHtmlContent;
        controller.mdTextContent = mdTextContent;
      }
    }

    /** Show method for dialogs */
    function onShow(scope, element, options, controller) {
      angular.element($document[0].body).addClass('md-dialog-is-showing');

      var dialogElement = element.find('md-dialog');

      // Once a dialog has `ng-cloak` applied on his template the dialog animation will not work properly.
      // This is a very common problem, so we have to notify the developer about this.
      if (dialogElement.hasClass('ng-cloak')) {
        var message = '$mdDialog: using `<md-dialog ng-cloak>` will affect the dialog opening animations.';
        $log.warn(message, element[0]);
      }

      captureParentAndFromToElements(options);
      configureAria(dialogElement, options);
      showBackdrop(scope, element, options);
      activateListeners(element, options);

      return dialogPopIn(element, options)
        .then(function() {
          lockScreenReader(element, options);
          warnDeprecatedActions();
          focusOnOpen();
        });

      /**
       * Check to see if they used the deprecated .md-actions class and log a warning
       */
      function warnDeprecatedActions() {
        if (element[0].querySelector('.md-actions')) {
          $log.warn('Using a class of md-actions is deprecated, please use <md-dialog-actions>.');
        }
      }

      /**
       * For alerts, focus on content... otherwise focus on
       * the close button (or equivalent)
       */
      function focusOnOpen() {
        if (options.focusOnOpen) {
          var target = $mdUtil.findFocusTarget(element) || findCloseButton() || dialogElement;
          target.focus();
        }

        /**
         * If no element with class dialog-close, try to find the last
         * button child in md-actions and assume it is a close button.
         *
         * If we find no actions at all, log a warning to the console.
         */
        function findCloseButton() {
          return element[0].querySelector('.dialog-close, md-dialog-actions button:last-child');
        }
      }
    }

    /**
     * Remove function for all dialogs
     */
    function onRemove(scope, element, options) {
      options.deactivateListeners();
      options.unlockScreenReader();
      options.hideBackdrop(options.$destroy);

      // Remove the focus traps that we added earlier for keeping focus within the dialog.
      if (topFocusTrap && topFocusTrap.parentNode) {
        topFocusTrap.parentNode.removeChild(topFocusTrap);
      }

      if (bottomFocusTrap && bottomFocusTrap.parentNode) {
        bottomFocusTrap.parentNode.removeChild(bottomFocusTrap);
      }

      // For navigation $destroy events, do a quick, non-animated removal,
      // but for normal closes (from clicks, etc) animate the removal
      return options.$destroy ? detachAndClean() : animateRemoval().then(detachAndClean);

      /**
       * For normal closes, animate the removal.
       * For forced closes (like $destroy events), skip the animations
       */
      function animateRemoval() {
        return dialogPopOut(element, options);
      }

      /**
       * Detach the element
       */
      function detachAndClean() {
        angular.element($document[0].body).removeClass('md-dialog-is-showing');

        // Reverse the container stretch if using a content element.
        if (options.contentElement) {
          options.reverseContainerStretch();
        }

        // Exposed cleanup function from the $mdCompiler.
        options.cleanupElement();

        // Restores the focus to the origin element if the last interaction upon opening was a keyboard.
        if (!options.$destroy && options.originInteraction === 'keyboard') {
          options.origin.focus();
        }
      }
    }

    function detectTheming(options) {
      // Once the user specifies a targetEvent, we will automatically try to find the correct
      // nested theme.
      var targetEl;
      if (options.targetEvent && options.targetEvent.target) {
        targetEl = angular.element(options.targetEvent.target);
      }

      var themeCtrl = targetEl && targetEl.controller('mdTheme');

      options.hasTheme = (!!themeCtrl);

      if (!options.hasTheme) {
        return;
      }

      options.themeWatch = themeCtrl.$shouldWatch;

      var theme = options.theme || themeCtrl.$mdTheme;

      if (theme) {
        options.scope.theme = theme;
      }

      var unwatch = themeCtrl.registerChanges(function (newTheme) {
        options.scope.theme = newTheme;

        if (!options.themeWatch) {
          unwatch();
        }
      });
    }

    /**
     * Capture originator/trigger/from/to element information (if available)
     * and the parent container for the dialog; defaults to the $rootElement
     * unless overridden in the options.parent
     */
    function captureParentAndFromToElements(options) {
          options.origin = angular.extend({
            element: null,
            bounds: null,
            focus: angular.noop
          }, options.origin || {});

          options.parent   = getDomElement(options.parent, $rootElement);
          options.closeTo  = getBoundingClientRect(getDomElement(options.closeTo));
          options.openFrom = getBoundingClientRect(getDomElement(options.openFrom));

          if (options.targetEvent) {
            options.origin = getBoundingClientRect(options.targetEvent.target, options.origin);
            options.originInteraction = $mdInteraction.getLastInteractionType();
          }


          /**
           * Identify the bounding RECT for the target element
           *
           */
          function getBoundingClientRect (element, orig) {
            var source = angular.element((element || {}));
            if (source && source.length) {
              // Compute and save the target element's bounding rect, so that if the
              // element is hidden when the dialog closes, we can shrink the dialog
              // back to the same position it expanded from.
              //
              // Checking if the source is a rect object or a DOM element
              var bounds = {top:0,left:0,height:0,width:0};
              var hasFn = angular.isFunction(source[0].getBoundingClientRect);

              return angular.extend(orig || {}, {
                  element : hasFn ? source : undefined,
                  bounds  : hasFn ? source[0].getBoundingClientRect() : angular.extend({}, bounds, source[0]),
                  focus   : angular.bind(source, source.focus),
              });
            }
          }

          /**
           * If the specifier is a simple string selector, then query for
           * the DOM element.
           */
          function getDomElement(element, defaultElement) {
            if (angular.isString(element)) {
              element = $document[0].querySelector(element);
            }

            // If we have a reference to a raw dom element, always wrap it in jqLite
            return angular.element(element || defaultElement);
          }

        }

    /**
     * Listen for escape keys and outside clicks to auto close
     */
    function activateListeners(element, options) {
      var window = angular.element($window);
      var onWindowResize = $mdUtil.debounce(function() {
        stretchDialogContainerToViewport(element, options);
      }, 60);

      var removeListeners = [];
      var smartClose = function() {
        // Only 'confirm' dialogs have a cancel button... escape/clickOutside will
        // cancel or fallback to hide.
        var closeFn = (options.$type == 'alert') ? $mdDialog.hide : $mdDialog.cancel;
        $mdUtil.nextTick(closeFn, true);
      };

      if (options.escapeToClose) {
        var parentTarget = options.parent;
        var keyHandlerFn = function(ev) {
          if (ev.keyCode === $mdConstant.KEY_CODE.ESCAPE) {
            ev.stopImmediatePropagation();
            ev.preventDefault();

            smartClose();
          }
        };

        // Add keydown listeners
        element.on('keydown', keyHandlerFn);
        parentTarget.on('keydown', keyHandlerFn);

        // Queue remove listeners function
        removeListeners.push(function() {

          element.off('keydown', keyHandlerFn);
          parentTarget.off('keydown', keyHandlerFn);

        });
      }

      // Register listener to update dialog on window resize
      window.on('resize', onWindowResize);

      removeListeners.push(function() {
        window.off('resize', onWindowResize);
      });

      if (options.clickOutsideToClose) {
        var target = element;
        var sourceElem;

        // Keep track of the element on which the mouse originally went down
        // so that we can only close the backdrop when the 'click' started on it.
        // A simple 'click' handler does not work,
        // it sets the target object as the element the mouse went down on.
        var mousedownHandler = function(ev) {
          sourceElem = ev.target;
        };

        // We check if our original element and the target is the backdrop
        // because if the original was the backdrop and the target was inside the dialog
        // we don't want to dialog to close.
        var mouseupHandler = function(ev) {
          if (sourceElem === target[0] && ev.target === target[0]) {
            ev.stopPropagation();
            ev.preventDefault();

            smartClose();
          }
        };

        // Add listeners
        target.on('mousedown', mousedownHandler);
        target.on('mouseup', mouseupHandler);

        // Queue remove listeners function
        removeListeners.push(function() {
          target.off('mousedown', mousedownHandler);
          target.off('mouseup', mouseupHandler);
        });
      }

      // Attach specific `remove` listener handler
      options.deactivateListeners = function() {
        removeListeners.forEach(function(removeFn) {
          removeFn();
        });
        options.deactivateListeners = null;
      };
    }

    /**
     * Show modal backdrop element...
     */
    function showBackdrop(scope, element, options) {

      if (options.disableParentScroll) {
        // !! DO this before creating the backdrop; since disableScrollAround()
        //    configures the scroll offset; which is used by mdBackDrop postLink()
        options.restoreScroll = $mdUtil.disableScrollAround(element, options.parent);
      }

      if (options.hasBackdrop) {
        options.backdrop = $mdUtil.createBackdrop(scope, "md-dialog-backdrop md-opaque");
        $animate.enter(options.backdrop, options.parent);
      }

      /**
       * Hide modal backdrop element...
       */
      options.hideBackdrop = function hideBackdrop($destroy) {
        if (options.backdrop) {
          if ($destroy) options.backdrop.remove();
          else              $animate.leave(options.backdrop);
        }


        if (options.disableParentScroll) {
          options.restoreScroll && options.restoreScroll();
          delete options.restoreScroll;
        }

        options.hideBackdrop = null;
      };
    }

    /**
     * Inject ARIA-specific attributes appropriate for Dialogs
     */
    function configureAria(element, options) {

      var role = (options.$type === 'alert') ? 'alertdialog' : 'dialog';
      var dialogContent = element.find('md-dialog-content');
      var existingDialogId = element.attr('id');
      var dialogContentId = 'dialogContent_' + (existingDialogId || $mdUtil.nextUid());

      element.attr({
        'role': role,
        'tabIndex': '-1'
      });

      if (dialogContent.length === 0) {
        dialogContent = element;
        // If the dialog element already had an ID, don't clobber it.
        if (existingDialogId) {
          dialogContentId = existingDialogId;
        }
      }

      dialogContent.attr('id', dialogContentId);
      element.attr('aria-describedby', dialogContentId);

      if (options.ariaLabel) {
        $mdAria.expect(element, 'aria-label', options.ariaLabel);
      }
      else {
        $mdAria.expectAsync(element, 'aria-label', function() {
          // If dialog title is specified, set aria-label with it
          // See https://github.com/angular/material/issues/10582
          if (options.title) {
            return options.title;
          } else {
            var words = dialogContent.text().split(/\s+/);
            if (words.length > 3) words = words.slice(0, 3).concat('...');
            return words.join(' ');
          }
        });
      }

      // Set up elements before and after the dialog content to capture focus and
      // redirect back into the dialog.
      topFocusTrap = document.createElement('div');
      topFocusTrap.classList.add('md-dialog-focus-trap');
      topFocusTrap.tabIndex = 0;

      bottomFocusTrap = topFocusTrap.cloneNode(false);

      // When focus is about to move out of the dialog, we want to intercept it and redirect it
      // back to the dialog element.
      var focusHandler = function() {
        element.focus();
      };
      topFocusTrap.addEventListener('focus', focusHandler);
      bottomFocusTrap.addEventListener('focus', focusHandler);

      // The top focus trap inserted immeidately before the md-dialog element (as a sibling).
      // The bottom focus trap is inserted at the very end of the md-dialog element (as a child).
      element[0].parentNode.insertBefore(topFocusTrap, element[0]);
      element.after(bottomFocusTrap);
    }

    /**
     * Prevents screen reader interaction behind modal window
     * on swipe interfaces
     */
    function lockScreenReader(element, options) {
      var isHidden = true;

      // get raw DOM node
      walkDOM(element[0]);

      options.unlockScreenReader = function () {
        isHidden = false;
        walkDOM(element[0]);

        options.unlockScreenReader = null;
      };

      /**
       * Get all of an element's parent elements up the DOM tree
       * @return {Array} The parent elements
       */
      function getParents(element) {
        var parents = [];
        while (element.parentNode) {
          if (element === document.body) {
            return parents;
          }
          var children = element.parentNode.children;
          for (var i = 0; i < children.length; i++) {
            // skip over child if it is an ascendant of the dialog
            // a script or style tag, or a live region.
            if (element !== children[i] &&
                !isNodeOneOf(children[i], ['SCRIPT', 'STYLE']) &&
                !children[i].hasAttribute('aria-live')) {
              parents.push(children[i]);
            }
          }
          element = element.parentNode;
        }
        return parents;
      }

      /**
       * Walk DOM to apply or remove aria-hidden on sibling nodes
       * and parent sibling nodes
       */
      function walkDOM(element) {
        var elements = getParents(element);
        for (var i = 0; i < elements.length; i++) {
          elements[i].setAttribute('aria-hidden', isHidden);
        }
      }
    }

    /**
     * Ensure the dialog container fill-stretches to the viewport
     */
    function stretchDialogContainerToViewport(container, options) {
      var isFixed = $window.getComputedStyle($document[0].body).position == 'fixed';
      var backdrop = options.backdrop ? $window.getComputedStyle(options.backdrop[0]) : null;
      var height = backdrop ? Math.min($document[0].body.clientHeight, Math.ceil(Math.abs(parseInt(backdrop.height, 10)))) : 0;

      var previousStyles = {
        top: container.css('top'),
        height: container.css('height')
      };

      // If the body is fixed, determine the distance to the viewport in relative from the parent.
      var parentTop = Math.abs(options.parent[0].getBoundingClientRect().top);

      container.css({
        top: (isFixed ? parentTop : 0) + 'px',
        height: height ? height + 'px' : '100%'
      });

      return function() {
        // Reverts the modified styles back to the previous values.
        // This is needed for contentElements, which should have the same styles after close
        // as before.
        container.css(previousStyles);
      };
    }

    /**
     *  Dialog open and pop-in animation
     */
    function dialogPopIn(container, options) {
      // Add the `md-dialog-container` to the DOM
      options.parent.append(container);
      options.reverseContainerStretch = stretchDialogContainerToViewport(container, options);

      var dialogEl = container.find('md-dialog');
      var animator = $mdUtil.dom.animator;
      var buildTranslateToOrigin = animator.calculateZoomToOrigin;
      var translateOptions = {transitionInClass: 'md-transition-in', transitionOutClass: 'md-transition-out'};
      var from = animator.toTransformCss(buildTranslateToOrigin(dialogEl, options.openFrom || options.origin));
      var to = animator.toTransformCss("");  // defaults to center display (or parent or $rootElement)

      dialogEl.toggleClass('md-dialog-fullscreen', !!options.fullscreen);

      return animator
        .translate3d(dialogEl, from, to, translateOptions)
        .then(function(animateReversal) {

          // Build a reversal translate function synced to this translation...
          options.reverseAnimate = function() {
            delete options.reverseAnimate;

            if (options.closeTo) {
              // Using the opposite classes to create a close animation to the closeTo element
              translateOptions = {transitionInClass: 'md-transition-out', transitionOutClass: 'md-transition-in'};
              from = to;
              to = animator.toTransformCss(buildTranslateToOrigin(dialogEl, options.closeTo));

              return animator
                .translate3d(dialogEl, from, to,translateOptions);
            }

            return animateReversal(
              to = animator.toTransformCss(
                // in case the origin element has moved or is hidden,
                // let's recalculate the translateCSS
                buildTranslateToOrigin(dialogEl, options.origin)
              )
            );

          };

          // Function to revert the generated animation styles on the dialog element.
          // Useful when using a contentElement instead of a template.
          options.clearAnimate = function() {
            delete options.clearAnimate;

            // Remove the transition classes, added from $animateCSS, since those can't be removed
            // by reversely running the animator.
            dialogEl.removeClass([
              translateOptions.transitionOutClass,
              translateOptions.transitionInClass
            ].join(' '));

            // Run the animation reversely to remove the previous added animation styles.
            return animator.translate3d(dialogEl, to, animator.toTransformCss(''), {});
          };

          return true;
        });
    }

    /**
     * Dialog close and pop-out animation
     */
    function dialogPopOut(container, options) {
      return options.reverseAnimate().then(function() {
        if (options.contentElement) {
          // When we use a contentElement, we want the element to be the same as before.
          // That means, that we have to clear all the animation properties, like transform.
          options.clearAnimate();
        }
      });
    }

    /**
     * Utility function to filter out raw DOM nodes
     */
    function isNodeOneOf(elem, nodeTypeArray) {
      if (nodeTypeArray.indexOf(elem.nodeName) !== -1) {
        return true;
      }
    }

  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.divider
 * @description Divider module!
 */
MdDividerDirective.$inject = ["$mdTheming"];
angular.module('material.components.divider', [
  'material.core'
])
  .directive('mdDivider', MdDividerDirective);

/**
 * @ngdoc directive
 * @name mdDivider
 * @module material.components.divider
 * @restrict E
 *
 * @description
 * Dividers group and separate content within lists and page layouts using strong visual and spatial distinctions. This divider is a thin rule, lightweight enough to not distract the user from content.
 *
 * @param {boolean=} md-inset Add this attribute to activate the inset divider style.
 * @usage
 * <hljs lang="html">
 * <md-divider></md-divider>
 *
 * <md-divider md-inset></md-divider>
 * </hljs>
 *
 */
function MdDividerDirective($mdTheming) {
  return {
    restrict: 'E',
    link: $mdTheming
  };
}

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc module
   * @name material.components.fabActions
   */
  MdFabActionsDirective.$inject = ["$mdUtil"];
  angular
    .module('material.components.fabActions', ['material.core'])
    .directive('mdFabActions', MdFabActionsDirective);

  /**
   * @ngdoc directive
   * @name mdFabActions
   * @module material.components.fabActions
   *
   * @restrict E
   *
   * @description
   * The `<md-fab-actions>` directive is used inside of a `<md-fab-speed-dial>` or
   * `<md-fab-toolbar>` directive to mark an element (or elements) as the actions and setup the
   * proper event listeners.
   *
   * @usage
   * See the `<md-fab-speed-dial>` or `<md-fab-toolbar>` directives for example usage.
   */
  function MdFabActionsDirective($mdUtil) {
    return {
      restrict: 'E',

      require: ['^?mdFabSpeedDial', '^?mdFabToolbar'],

      compile: function(element, attributes) {
        var children = element.children();

        var hasNgRepeat = $mdUtil.prefixer().hasAttribute(children, 'ng-repeat');

        // Support both ng-repeat and static content
        if (hasNgRepeat) {
          children.addClass('md-fab-action-item');
        } else {
          // Wrap every child in a new div and add a class that we can scale/fling independently
          children.wrap('<div class="md-fab-action-item">');
        }
      }
    };
  }

})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  MdFabController.$inject = ["$scope", "$element", "$animate", "$mdUtil", "$mdConstant", "$timeout"];
  angular.module('material.components.fabShared', ['material.core'])
    .controller('MdFabController', MdFabController);

  function MdFabController($scope, $element, $animate, $mdUtil, $mdConstant, $timeout) {
    var vm = this;
    var initialAnimationAttempts = 0;

    // NOTE: We use async eval(s) below to avoid conflicts with any existing digest loops

    vm.open = function() {
      $scope.$evalAsync("vm.isOpen = true");
    };

    vm.close = function() {
      // Async eval to avoid conflicts with existing digest loops
      $scope.$evalAsync("vm.isOpen = false");

      // Focus the trigger when the element closes so users can still tab to the next item
      $element.find('md-fab-trigger')[0].focus();
    };

    // Toggle the open/close state when the trigger is clicked
    vm.toggle = function() {
      $scope.$evalAsync("vm.isOpen = !vm.isOpen");
    };

    /*
     * AngularJS Lifecycle hook for newer AngularJS versions.
     * Bindings are not guaranteed to have been assigned in the controller, but they are in the $onInit hook.
     */
    vm.$onInit = function() {
      setupDefaults();
      setupListeners();
      setupWatchers();

      fireInitialAnimations();
    };

    // For AngularJS 1.4 and older, where there are no lifecycle hooks but bindings are pre-assigned,
    // manually call the $onInit hook.
    if (angular.version.major === 1 && angular.version.minor <= 4) {
      this.$onInit();
    }

    function setupDefaults() {
      // Set the default direction to 'down' if none is specified
      vm.direction = vm.direction || 'down';

      // Set the default to be closed
      vm.isOpen = vm.isOpen || false;

      // Start the keyboard interaction at the first action
      resetActionIndex();

      // Add an animations waiting class so we know not to run
      $element.addClass('md-animations-waiting');
    }

    function setupListeners() {
      var eventTypes = [
        'click', 'focusin', 'focusout'
      ];

      // Add our listeners
      angular.forEach(eventTypes, function(eventType) {
        $element.on(eventType, parseEvents);
      });

      // Remove our listeners when destroyed
      $scope.$on('$destroy', function() {
        angular.forEach(eventTypes, function(eventType) {
          $element.off(eventType, parseEvents);
        });

        // remove any attached keyboard handlers in case element is removed while
        // speed dial is open
        disableKeyboard();
      });
    }

    var closeTimeout;
    function parseEvents(event) {
      // If the event is a click, just handle it
      if (event.type == 'click') {
        handleItemClick(event);
      }

      // If we focusout, set a timeout to close the element
      if (event.type == 'focusout' && !closeTimeout) {
        closeTimeout = $timeout(function() {
          vm.close();
        }, 100, false);
      }

      // If we see a focusin and there is a timeout about to run, cancel it so we stay open
      if (event.type == 'focusin' && closeTimeout) {
        $timeout.cancel(closeTimeout);
        closeTimeout = null;
      }
    }

    function resetActionIndex() {
      vm.currentActionIndex = -1;
    }

    function setupWatchers() {
      // Watch for changes to the direction and update classes/attributes
      $scope.$watch('vm.direction', function(newDir, oldDir) {
        // Add the appropriate classes so we can target the direction in the CSS
        $animate.removeClass($element, 'md-' + oldDir);
        $animate.addClass($element, 'md-' + newDir);

        // Reset the action index since it may have changed
        resetActionIndex();
      });

      var trigger, actions;

      // Watch for changes to md-open
      $scope.$watch('vm.isOpen', function(isOpen) {
        // Reset the action index since it may have changed
        resetActionIndex();

        // We can't get the trigger/actions outside of the watch because the component hasn't been
        // linked yet, so we wait until the first watch fires to cache them.
        if (!trigger || !actions) {
          trigger = getTriggerElement();
          actions = getActionsElement();
        }

        if (isOpen) {
          enableKeyboard();
        } else {
          disableKeyboard();
        }

        var toAdd = isOpen ? 'md-is-open' : '';
        var toRemove = isOpen ? '' : 'md-is-open';

        // Set the proper ARIA attributes
        trigger.attr('aria-haspopup', true);
        trigger.attr('aria-expanded', isOpen);
        actions.attr('aria-hidden', !isOpen);

        // Animate the CSS classes
        $animate.setClass($element, toAdd, toRemove);
      });
    }

    function fireInitialAnimations() {
      // If the element is actually visible on the screen
      if ($element[0].scrollHeight > 0) {
        // Fire our animation
        $animate.addClass($element, '_md-animations-ready').then(function() {
          // Remove the waiting class
          $element.removeClass('md-animations-waiting');
        });
      }

      // Otherwise, try for up to 1 second before giving up
      else if (initialAnimationAttempts < 10) {
        $timeout(fireInitialAnimations, 100);

        // Increment our counter
        initialAnimationAttempts = initialAnimationAttempts + 1;
      }
    }

    function enableKeyboard() {
      $element.on('keydown', keyPressed);

      // On the next tick, setup a check for outside clicks; we do this on the next tick to avoid
      // clicks/touches that result in the isOpen attribute changing (e.g. a bound radio button)
      $mdUtil.nextTick(function() {
        angular.element(document).on('click touchend', checkForOutsideClick);
      });

      // TODO: On desktop, we should be able to reset the indexes so you cannot tab through, but
      // this breaks accessibility, especially on mobile, since you have no arrow keys to press
      // resetActionTabIndexes();
    }

    function disableKeyboard() {
      $element.off('keydown', keyPressed);
      angular.element(document).off('click touchend', checkForOutsideClick);
    }

    function checkForOutsideClick(event) {
      if (event.target) {
        var closestTrigger = $mdUtil.getClosest(event.target, 'md-fab-trigger');
        var closestActions = $mdUtil.getClosest(event.target, 'md-fab-actions');

        if (!closestTrigger && !closestActions) {
          vm.close();
        }
      }
    }

    function keyPressed(event) {
      switch (event.which) {
        case $mdConstant.KEY_CODE.ESCAPE: vm.close(); event.preventDefault(); return false;
        case $mdConstant.KEY_CODE.LEFT_ARROW: doKeyLeft(event); return false;
        case $mdConstant.KEY_CODE.UP_ARROW: doKeyUp(event); return false;
        case $mdConstant.KEY_CODE.RIGHT_ARROW: doKeyRight(event); return false;
        case $mdConstant.KEY_CODE.DOWN_ARROW: doKeyDown(event); return false;
      }
    }

    function doActionPrev(event) {
      focusAction(event, -1);
    }

    function doActionNext(event) {
      focusAction(event, 1);
    }

    function focusAction(event, direction) {
      var actions = resetActionTabIndexes();

      // Increment/decrement the counter with restrictions
      vm.currentActionIndex = vm.currentActionIndex + direction;
      vm.currentActionIndex = Math.min(actions.length - 1, vm.currentActionIndex);
      vm.currentActionIndex = Math.max(0, vm.currentActionIndex);

      // Focus the element
      var focusElement =  angular.element(actions[vm.currentActionIndex]).children()[0];
      angular.element(focusElement).attr('tabindex', 0);
      focusElement.focus();

      // Make sure the event doesn't bubble and cause something else
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    function resetActionTabIndexes() {
      // Grab all of the actions
      var actions = getActionsElement()[0].querySelectorAll('.md-fab-action-item');

      // Disable all other actions for tabbing
      angular.forEach(actions, function(action) {
        angular.element(angular.element(action).children()[0]).attr('tabindex', -1);
      });

      return actions;
    }

    function doKeyLeft(event) {
      if (vm.direction === 'left') {
        doActionNext(event);
      } else {
        doActionPrev(event);
      }
    }

    function doKeyUp(event) {
      if (vm.direction === 'down') {
        doActionPrev(event);
      } else {
        doActionNext(event);
      }
    }

    function doKeyRight(event) {
      if (vm.direction === 'left') {
        doActionPrev(event);
      } else {
        doActionNext(event);
      }
    }

    function doKeyDown(event) {
      if (vm.direction === 'up') {
        doActionPrev(event);
      } else {
        doActionNext(event);
      }
    }

    function isTrigger(element) {
      return $mdUtil.getClosest(element, 'md-fab-trigger');
    }

    function isAction(element) {
      return $mdUtil.getClosest(element, 'md-fab-actions');
    }

    function handleItemClick(event) {
      if (isTrigger(event.target)) {
        vm.toggle();
      }

      if (isAction(event.target)) {
        vm.close();
      }
    }

    function getTriggerElement() {
      return $element.find('md-fab-trigger');
    }

    function getActionsElement() {
      return $element.find('md-fab-actions');
    }
  }
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * The duration of the CSS animation in milliseconds.
   *
   * @type {number}
   */
  MdFabSpeedDialFlingAnimation.$inject = ["$timeout"];
  MdFabSpeedDialScaleAnimation.$inject = ["$timeout"];
  var cssAnimationDuration = 300;

  /**
   * @ngdoc module
   * @name material.components.fabSpeedDial
   */
  angular
    // Declare our module
    .module('material.components.fabSpeedDial', [
      'material.core',
      'material.components.fabShared',
      'material.components.fabActions'
    ])

    // Register our directive
    .directive('mdFabSpeedDial', MdFabSpeedDialDirective)

    // Register our custom animations
    .animation('.md-fling', MdFabSpeedDialFlingAnimation)
    .animation('.md-scale', MdFabSpeedDialScaleAnimation)

    // Register a service for each animation so that we can easily inject them into unit tests
    .service('mdFabSpeedDialFlingAnimation', MdFabSpeedDialFlingAnimation)
    .service('mdFabSpeedDialScaleAnimation', MdFabSpeedDialScaleAnimation);

  /**
   * @ngdoc directive
   * @name mdFabSpeedDial
   * @module material.components.fabSpeedDial
   *
   * @restrict E
   *
   * @description
   * The `<md-fab-speed-dial>` directive is used to present a series of popup elements (usually
   * `<md-button>`s) for quick access to common actions.
   *
   * There are currently two animations available by applying one of the following classes to
   * the component:
   *
   *  - `md-fling` - The speed dial items appear from underneath the trigger and move into their
   *    appropriate positions.
   *  - `md-scale` - The speed dial items appear in their proper places by scaling from 0% to 100%.
   *
   * You may also easily position the trigger by applying one one of the following classes to the
   * `<md-fab-speed-dial>` element:
   *  - `md-fab-top-left`
   *  - `md-fab-top-right`
   *  - `md-fab-bottom-left`
   *  - `md-fab-bottom-right`
   *
   * These CSS classes use `position: absolute`, so you need to ensure that the container element
   * also uses `position: absolute` or `position: relative` in order for them to work.
   *
   * Additionally, you may use the standard `ng-mouseenter` and `ng-mouseleave` directives to
   * open or close the speed dial. However, if you wish to allow users to hover over the empty
   * space where the actions will appear, you must also add the `md-hover-full` class to the speed
   * dial element. Without this, the hover effect will only occur on top of the trigger.
   *
   * See the demos for more information.
   *
   * ## Troubleshooting
   *
   * If your speed dial shows the closing animation upon launch, you may need to use `ng-cloak` on
   * the parent container to ensure that it is only visible once ready. We have plans to remove this
   * necessity in the future.
   *
   * @usage
   * <hljs lang="html">
   * <md-fab-speed-dial md-direction="up" class="md-fling">
   *   <md-fab-trigger>
   *     <md-button aria-label="Add..."><md-icon md-svg-src="/img/icons/plus.svg"></md-icon></md-button>
   *   </md-fab-trigger>
   *
   *   <md-fab-actions>
   *     <md-button aria-label="Add User">
   *       <md-icon md-svg-src="/img/icons/user.svg"></md-icon>
   *     </md-button>
   *
   *     <md-button aria-label="Add Group">
   *       <md-icon md-svg-src="/img/icons/group.svg"></md-icon>
   *     </md-button>
   *   </md-fab-actions>
   * </md-fab-speed-dial>
   * </hljs>
   *
   * @param {string} md-direction From which direction you would like the speed dial to appear
   * relative to the trigger element.
   * @param {expression=} md-open Programmatically control whether or not the speed-dial is visible.
   */
  function MdFabSpeedDialDirective() {
    return {
      restrict: 'E',

      scope: {
        direction: '@?mdDirection',
        isOpen: '=?mdOpen'
      },

      bindToController: true,
      controller: 'MdFabController',
      controllerAs: 'vm',

      link: FabSpeedDialLink
    };

    function FabSpeedDialLink(scope, element) {
      // Prepend an element to hold our CSS variables so we can use them in the animations below
      element.prepend('<div class="_md-css-variables"></div>');
    }
  }

  function MdFabSpeedDialFlingAnimation($timeout) {
    function delayDone(done) { $timeout(done, cssAnimationDuration, false); }

    function runAnimation(element) {
      // Don't run if we are still waiting and we are not ready
      if (element.hasClass('md-animations-waiting') && !element.hasClass('_md-animations-ready')) {
        return;
      }

      var el = element[0];
      var ctrl = element.controller('mdFabSpeedDial');
      var items = el.querySelectorAll('.md-fab-action-item');

      // Grab our trigger element
      var triggerElement = el.querySelector('md-fab-trigger');

      // Grab our element which stores CSS variables
      var variablesElement = el.querySelector('._md-css-variables');

      // Setup JS variables based on our CSS variables
      var startZIndex = parseInt(window.getComputedStyle(variablesElement).zIndex);

      // Always reset the items to their natural position/state
      angular.forEach(items, function(item, index) {
        var styles = item.style;

        styles.transform = styles.webkitTransform = '';
        styles.transitionDelay = '';
        styles.opacity = 1;

        // Make the items closest to the trigger have the highest z-index
        styles.zIndex = (items.length - index) + startZIndex;
      });

      // Set the trigger to be above all of the actions so they disappear behind it.
      triggerElement.style.zIndex = startZIndex + items.length + 1;

      // If the control is closed, hide the items behind the trigger
      if (!ctrl.isOpen) {
        angular.forEach(items, function(item, index) {
          var newPosition, axis;
          var styles = item.style;

          // Make sure to account for differences in the dimensions of the trigger verses the items
          // so that we can properly center everything; this helps hide the item's shadows behind
          // the trigger.
          var triggerItemHeightOffset = (triggerElement.clientHeight - item.clientHeight) / 2;
          var triggerItemWidthOffset = (triggerElement.clientWidth - item.clientWidth) / 2;

          switch (ctrl.direction) {
            case 'up':
              newPosition = (item.scrollHeight * (index + 1) + triggerItemHeightOffset);
              axis = 'Y';
              break;
            case 'down':
              newPosition = -(item.scrollHeight * (index + 1) + triggerItemHeightOffset);
              axis = 'Y';
              break;
            case 'left':
              newPosition = (item.scrollWidth * (index + 1) + triggerItemWidthOffset);
              axis = 'X';
              break;
            case 'right':
              newPosition = -(item.scrollWidth * (index + 1) + triggerItemWidthOffset);
              axis = 'X';
              break;
          }

          var newTranslate = 'translate' + axis + '(' + newPosition + 'px)';

          styles.transform = styles.webkitTransform = newTranslate;
        });
      }
    }

    return {
      addClass: function(element, className, done) {
        if (element.hasClass('md-fling')) {
          runAnimation(element);
          delayDone(done);
        } else {
          done();
        }
      },
      removeClass: function(element, className, done) {
        runAnimation(element);
        delayDone(done);
      }
    };
  }

  function MdFabSpeedDialScaleAnimation($timeout) {
    function delayDone(done) { $timeout(done, cssAnimationDuration, false); }

    var delay = 65;

    function runAnimation(element) {
      var el = element[0];
      var ctrl = element.controller('mdFabSpeedDial');
      var items = el.querySelectorAll('.md-fab-action-item');

      // Grab our element which stores CSS variables
      var variablesElement = el.querySelector('._md-css-variables');

      // Setup JS variables based on our CSS variables
      var startZIndex = parseInt(window.getComputedStyle(variablesElement).zIndex);

      // Always reset the items to their natural position/state
      angular.forEach(items, function(item, index) {
        var styles = item.style,
          offsetDelay = index * delay;

        styles.opacity = ctrl.isOpen ? 1 : 0;
        styles.transform = styles.webkitTransform = ctrl.isOpen ? 'scale(1)' : 'scale(0)';
        styles.transitionDelay = (ctrl.isOpen ? offsetDelay : (items.length - offsetDelay)) + 'ms';

        // Make the items closest to the trigger have the highest z-index
        styles.zIndex = (items.length - index) + startZIndex;
      });
    }

    return {
      addClass: function(element, className, done) {
        runAnimation(element);
        delayDone(done);
      },

      removeClass: function(element, className, done) {
        runAnimation(element);
        delayDone(done);
      }
    };
  }
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc module
   * @name material.components.fabToolbar
   */
  angular
    // Declare our module
    .module('material.components.fabToolbar', [
      'material.core',
      'material.components.fabShared',
      'material.components.fabActions'
    ])

    // Register our directive
    .directive('mdFabToolbar', MdFabToolbarDirective)

    // Register our custom animations
    .animation('.md-fab-toolbar', MdFabToolbarAnimation)

    // Register a service for the animation so that we can easily inject it into unit tests
    .service('mdFabToolbarAnimation', MdFabToolbarAnimation);

  /**
   * @ngdoc directive
   * @name mdFabToolbar
   * @module material.components.fabToolbar
   *
   * @restrict E
   *
   * @description
   *
   * The `<md-fab-toolbar>` directive is used to present a toolbar of elements (usually `<md-button>`s)
   * for quick access to common actions when a floating action button is activated (via click or
   * keyboard navigation).
   *
   * You may also easily position the trigger by applying one one of the following classes to the
   * `<md-fab-toolbar>` element:
   *  - `md-fab-top-left`
   *  - `md-fab-top-right`
   *  - `md-fab-bottom-left`
   *  - `md-fab-bottom-right`
   *
   * These CSS classes use `position: absolute`, so you need to ensure that the container element
   * also uses `position: absolute` or `position: relative` in order for them to work.
   *
   * @usage
   *
   * <hljs lang="html">
   * <md-fab-toolbar md-direction='left'>
   *   <md-fab-trigger>
   *     <md-button aria-label="Add..."><md-icon md-svg-src="/img/icons/plus.svg"></md-icon></md-button>
   *   </md-fab-trigger>
   *
   *   <md-toolbar>
   *    <md-fab-actions>
   *      <md-button aria-label="Add User">
   *        <md-icon md-svg-src="/img/icons/user.svg"></md-icon>
   *      </md-button>
   *
   *      <md-button aria-label="Add Group">
   *        <md-icon md-svg-src="/img/icons/group.svg"></md-icon>
   *      </md-button>
   *    </md-fab-actions>
   *   </md-toolbar>
   * </md-fab-toolbar>
   * </hljs>
   *
   * @param {string} md-direction From which direction you would like the toolbar items to appear
   * relative to the trigger element. Supports `left` and `right` directions.
   * @param {expression=} md-open Programmatically control whether or not the toolbar is visible.
   */
  function MdFabToolbarDirective() {
    return {
      restrict: 'E',
      transclude: true,
      template: '<div class="md-fab-toolbar-wrapper">' +
      '  <div class="md-fab-toolbar-content" ng-transclude></div>' +
      '</div>',

      scope: {
        direction: '@?mdDirection',
        isOpen: '=?mdOpen'
      },

      bindToController: true,
      controller: 'MdFabController',
      controllerAs: 'vm',

      link: link
    };

    function link(scope, element, attributes) {
      // Add the base class for animations
      element.addClass('md-fab-toolbar');

      // Prepend the background element to the trigger's button
      element.find('md-fab-trigger').find('button')
        .prepend('<div class="md-fab-toolbar-background"></div>');
    }
  }

  function MdFabToolbarAnimation() {

    function runAnimation(element, className, done) {
      // If no className was specified, don't do anything
      if (!className) {
        return;
      }

      var el = element[0];
      var ctrl = element.controller('mdFabToolbar');

      // Grab the relevant child elements
      var backgroundElement = el.querySelector('.md-fab-toolbar-background');
      var triggerElement = el.querySelector('md-fab-trigger button');
      var toolbarElement = el.querySelector('md-toolbar');
      var iconElement = el.querySelector('md-fab-trigger button md-icon');
      var actions = element.find('md-fab-actions').children();

      // If we have both elements, use them to position the new background
      if (triggerElement && backgroundElement) {
        // Get our variables
        var color = window.getComputedStyle(triggerElement).getPropertyValue('background-color');
        var width = el.offsetWidth;
        var height = el.offsetHeight;

        // Make it twice as big as it should be since we scale from the center
        var scale = 2 * (width / triggerElement.offsetWidth);

        // Set some basic styles no matter what animation we're doing
        backgroundElement.style.backgroundColor = color;
        backgroundElement.style.borderRadius = width + 'px';

        // If we're open
        if (ctrl.isOpen) {
          // Turn on toolbar pointer events when closed
          toolbarElement.style.pointerEvents = 'inherit';

          backgroundElement.style.width = triggerElement.offsetWidth + 'px';
          backgroundElement.style.height = triggerElement.offsetHeight + 'px';
          backgroundElement.style.transform = 'scale(' + scale + ')';

          // Set the next close animation to have the proper delays
          backgroundElement.style.transitionDelay = '0ms';
          iconElement && (iconElement.style.transitionDelay = '.3s');

          // Apply a transition delay to actions
          angular.forEach(actions, function(action, index) {
            action.style.transitionDelay = (actions.length - index) * 25 + 'ms';
          });
        } else {
          // Turn off toolbar pointer events when closed
          toolbarElement.style.pointerEvents = 'none';

          // Scale it back down to the trigger's size
          backgroundElement.style.transform = 'scale(1)';

          // Reset the position
          backgroundElement.style.top = '0';

          if (element.hasClass('md-right')) {
            backgroundElement.style.left = '0';
            backgroundElement.style.right = null;
          }

          if (element.hasClass('md-left')) {
            backgroundElement.style.right = '0';
            backgroundElement.style.left = null;
          }

          // Set the next open animation to have the proper delays
          backgroundElement.style.transitionDelay = '200ms';
          iconElement && (iconElement.style.transitionDelay = '0ms');

          // Apply a transition delay to actions
          angular.forEach(actions, function(action, index) {
            action.style.transitionDelay = 200 + (index * 25) + 'ms';
          });
        }
      }
    }

    return {
      addClass: function(element, className, done) {
        runAnimation(element, className, done);
        done();
      },

      removeClass: function(element, className, done) {
        runAnimation(element, className, done);
        done();
      }
    };
  }
})();

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.gridList
 */
GridListController.$inject = ["$mdUtil"];
GridLayoutFactory.$inject = ["$mdUtil"];
GridListDirective.$inject = ["$interpolate", "$mdConstant", "$mdGridLayout", "$mdMedia", "$mdUtil"];
GridTileDirective.$inject = ["$mdMedia"];
angular.module('material.components.gridList', ['material.core'])
       .directive('mdGridList', GridListDirective)
       .directive('mdGridTile', GridTileDirective)
       .directive('mdGridTileFooter', GridTileCaptionDirective)
       .directive('mdGridTileHeader', GridTileCaptionDirective)
       .factory('$mdGridLayout', GridLayoutFactory);

/**
 * @ngdoc directive
 * @name mdGridList
 * @module material.components.gridList
 * @restrict E
 * @description
 * Grid lists are an alternative to standard list views. Grid lists are distinct
 * from grids used for layouts and other visual presentations.
 *
 * A grid list is best suited to presenting a homogenous data type, typically
 * images, and is optimized for visual comprehension and differentiating between
 * like data types.
 *
 * A grid list is a continuous element consisting of tessellated, regular
 * subdivisions called cells that contain tiles (`md-grid-tile`).
 *
 * <img src="//material-design.storage.googleapis.com/publish/v_2/material_ext_publish/0Bx4BSt6jniD7OVlEaXZ5YmU1Xzg/components_grids_usage2.png"
 *    style="width: 300px; height: auto; margin-right: 16px;" alt="Concept of grid explained visually">
 * <img src="//material-design.storage.googleapis.com/publish/v_2/material_ext_publish/0Bx4BSt6jniD7VGhsOE5idWlJWXM/components_grids_usage3.png"
 *    style="width: 300px; height: auto;" alt="Grid concepts legend">
 *
 * Cells are arrayed vertically and horizontally within the grid.
 *
 * Tiles hold content and can span one or more cells vertically or horizontally.
 *
 * ### Responsive Attributes
 *
 * The `md-grid-list` directive supports "responsive" attributes, which allow
 * different `md-cols`, `md-gutter` and `md-row-height` values depending on the
 * currently matching media query.
 *
 * In order to set a responsive attribute, first define the fallback value with
 * the standard attribute name, then add additional attributes with the
 * following convention: `{base-attribute-name}-{media-query-name}="{value}"`
 * (ie. `md-cols-lg="8"`)
 *
 * @param {number} md-cols Number of columns in the grid.
 * @param {string} md-row-height One of
 * <ul>
 *   <li>CSS length - Fixed height rows (eg. `8px` or `1rem`)</li>
 *   <li>`{width}:{height}` - Ratio of width to height (eg.
 *   `md-row-height="16:9"`)</li>
 *   <li>`"fit"` - Height will be determined by subdividing the available
 *   height by the number of rows</li>
 * </ul>
 * @param {string=} md-gutter The amount of space between tiles in CSS units
 *     (default 1px)
 * @param {expression=} md-on-layout Expression to evaluate after layout. Event
 *     object is available as `$event`, and contains performance information.
 *
 * @usage
 * Basic:
 * <hljs lang="html">
 * <md-grid-list md-cols="5" md-gutter="1em" md-row-height="4:3">
 *   <md-grid-tile></md-grid-tile>
 * </md-grid-list>
 * </hljs>
 *
 * Fixed-height rows:
 * <hljs lang="html">
 * <md-grid-list md-cols="4" md-row-height="200px" ...>
 *   <md-grid-tile></md-grid-tile>
 * </md-grid-list>
 * </hljs>
 *
 * Fit rows:
 * <hljs lang="html">
 * <md-grid-list md-cols="4" md-row-height="fit" style="height: 400px;" ...>
 *   <md-grid-tile></md-grid-tile>
 * </md-grid-list>
 * </hljs>
 *
 * Using responsive attributes:
 * <hljs lang="html">
 * <md-grid-list
 *     md-cols-sm="2"
 *     md-cols-md="4"
 *     md-cols-lg="8"
 *     md-cols-gt-lg="12"
 *     ...>
 *   <md-grid-tile></md-grid-tile>
 * </md-grid-list>
 * </hljs>
 */
function GridListDirective($interpolate, $mdConstant, $mdGridLayout, $mdMedia, $mdUtil) {
  return {
    restrict: 'E',
    controller: GridListController,
    scope: {
      mdOnLayout: '&'
    },
    link: postLink
  };

  function postLink(scope, element, attrs, ctrl) {
    element.addClass('_md');     // private md component indicator for styling

    // Apply semantics
    element.attr('role', 'list');

    // Provide the controller with a way to trigger layouts.
    ctrl.layoutDelegate = layoutDelegate;

    var invalidateLayout = angular.bind(ctrl, ctrl.invalidateLayout),
        unwatchAttrs = watchMedia();
      scope.$on('$destroy', unwatchMedia);

    /**
     * Watches for changes in media, invalidating layout as necessary.
     */
    function watchMedia() {
      for (var mediaName in $mdConstant.MEDIA) {
        $mdMedia(mediaName); // initialize
        $mdMedia.getQuery($mdConstant.MEDIA[mediaName])
            .addListener(invalidateLayout);
      }
      return $mdMedia.watchResponsiveAttributes(
          ['md-cols', 'md-row-height', 'md-gutter'], attrs, layoutIfMediaMatch);
    }

    function unwatchMedia() {
      ctrl.layoutDelegate = angular.noop;

      unwatchAttrs();
      for (var mediaName in $mdConstant.MEDIA) {
        $mdMedia.getQuery($mdConstant.MEDIA[mediaName])
            .removeListener(invalidateLayout);
      }
    }

    /**
     * Performs grid layout if the provided mediaName matches the currently
     * active media type.
     */
    function layoutIfMediaMatch(mediaName) {
      if (mediaName == null) {
        // TODO(shyndman): It would be nice to only layout if we have
        // instances of attributes using this media type
        ctrl.invalidateLayout();
      } else if ($mdMedia(mediaName)) {
        ctrl.invalidateLayout();
      }
    }

    var lastLayoutProps;

    /**
     * Invokes the layout engine, and uses its results to lay out our
     * tile elements.
     *
     * @param {boolean} tilesInvalidated Whether tiles have been
     *    added/removed/moved since the last layout. This is to avoid situations
     *    where tiles are replaced with properties identical to their removed
     *    counterparts.
     */
    function layoutDelegate(tilesInvalidated) {
      var tiles = getTileElements();
      var props = {
        tileSpans: getTileSpans(tiles),
        colCount: getColumnCount(),
        rowMode: getRowMode(),
        rowHeight: getRowHeight(),
        gutter: getGutter()
      };

      if (!tilesInvalidated && angular.equals(props, lastLayoutProps)) {
        return;
      }

      var performance =
        $mdGridLayout(props.colCount, props.tileSpans, tiles)
          .map(function(tilePositions, rowCount) {
            return {
              grid: {
                element: element,
                style: getGridStyle(props.colCount, rowCount,
                    props.gutter, props.rowMode, props.rowHeight)
              },
              tiles: tilePositions.map(function(ps, i) {
                return {
                  element: angular.element(tiles[i]),
                  style: getTileStyle(ps.position, ps.spans,
                      props.colCount, rowCount,
                      props.gutter, props.rowMode, props.rowHeight)
                };
              })
            };
          })
          .reflow()
          .performance();

      // Report layout
      scope.mdOnLayout({
        $event: {
          performance: performance
        }
      });

      lastLayoutProps = props;
    }

    // Use $interpolate to do some simple string interpolation as a convenience.

    var startSymbol = $interpolate.startSymbol();
    var endSymbol = $interpolate.endSymbol();

    // Returns an expression wrapped in the interpolator's start and end symbols.
    function expr(exprStr) {
      return startSymbol + exprStr + endSymbol;
    }

    // The amount of space a single 1x1 tile would take up (either width or height), used as
    // a basis for other calculations. This consists of taking the base size percent (as would be
    // if evenly dividing the size between cells), and then subtracting the size of one gutter.
    // However, since there are no gutters on the edges, each tile only uses a fration
    // (gutterShare = numGutters / numCells) of the gutter size. (Imagine having one gutter per
    // tile, and then breaking up the extra gutter on the edge evenly among the cells).
    var UNIT = $interpolate(expr('share') + '% - (' + expr('gutter') + ' * ' + expr('gutterShare') + ')');

    // The horizontal or vertical position of a tile, e.g., the 'top' or 'left' property value.
    // The position comes the size of a 1x1 tile plus gutter for each previous tile in the
    // row/column (offset).
    var POSITION  = $interpolate('calc((' + expr('unit') + ' + ' + expr('gutter') + ') * ' + expr('offset') + ')');

    // The actual size of a tile, e.g., width or height, taking rowSpan or colSpan into account.
    // This is computed by multiplying the base unit by the rowSpan/colSpan, and then adding back
    // in the space that the gutter would normally have used (which was already accounted for in
    // the base unit calculation).
    var DIMENSION = $interpolate('calc((' + expr('unit') + ') * ' + expr('span') + ' + (' + expr('span') + ' - 1) * ' + expr('gutter') + ')');

    /**
     * Gets the styles applied to a tile element described by the given parameters.
     * @param {{row: number, col: number}} position The row and column indices of the tile.
     * @param {{row: number, col: number}} spans The rowSpan and colSpan of the tile.
     * @param {number} colCount The number of columns.
     * @param {number} rowCount The number of rows.
     * @param {string} gutter The amount of space between tiles. This will be something like
     *     '5px' or '2em'.
     * @param {string} rowMode The row height mode. Can be one of:
     *     'fixed': all rows have a fixed size, given by rowHeight,
     *     'ratio': row height defined as a ratio to width, or
     *     'fit': fit to the grid-list element height, divinding evenly among rows.
     * @param {string|number} rowHeight The height of a row. This is only used for 'fixed' mode and
     *     for 'ratio' mode. For 'ratio' mode, this is the *ratio* of width-to-height (e.g., 0.75).
     * @returns {Object} Map of CSS properties to be applied to the style element. Will define
     *     values for top, left, width, height, marginTop, and paddingTop.
     */
    function getTileStyle(position, spans, colCount, rowCount, gutter, rowMode, rowHeight) {
      // TODO(shyndman): There are style caching opportunities here.

      // Percent of the available horizontal space that one column takes up.
      var hShare = (1 / colCount) * 100;

      // Fraction of the gutter size that each column takes up.
      var hGutterShare = (colCount - 1) / colCount;

      // Base horizontal size of a column.
      var hUnit = UNIT({share: hShare, gutterShare: hGutterShare, gutter: gutter});

      // The width and horizontal position of each tile is always calculated the same way, but the
      // height and vertical position depends on the rowMode.
      var style = (!$mdUtil.isRtl(attrs)) ? {
          left: POSITION({ unit: hUnit, offset: position.col, gutter: gutter }),
          width: DIMENSION({ unit: hUnit, span: spans.col, gutter: gutter }),
          // resets
          paddingTop: '',
          marginTop: '',
          top: '',
          height: ''
        } : {
        right: POSITION({ unit: hUnit, offset: position.col, gutter: gutter }),
        width: DIMENSION({ unit: hUnit, span: spans.col, gutter: gutter }),
        // resets
        paddingTop: '',
        marginTop: '',
        top: '',
        height: ''
      };

      switch (rowMode) {
        case 'fixed':
          // In fixed mode, simply use the given rowHeight.
          style.top = POSITION({ unit: rowHeight, offset: position.row, gutter: gutter });
          style.height = DIMENSION({ unit: rowHeight, span: spans.row, gutter: gutter });
          break;

        case 'ratio':
          // Percent of the available vertical space that one row takes up. Here, rowHeight holds
          // the ratio value. For example, if the width:height ratio is 4:3, rowHeight = 1.333.
          var vShare = hShare / rowHeight;

          // Base veritcal size of a row.
          var vUnit = UNIT({ share: vShare, gutterShare: hGutterShare, gutter: gutter });

          // padidngTop and marginTop are used to maintain the given aspect ratio, as
          // a percentage-based value for these properties is applied to the *width* of the
          // containing block. See http://www.w3.org/TR/CSS2/box.html#margin-properties
          style.paddingTop = DIMENSION({ unit: vUnit, span: spans.row, gutter: gutter});
          style.marginTop = POSITION({ unit: vUnit, offset: position.row, gutter: gutter });
          break;

        case 'fit':
          // Fraction of the gutter size that each column takes up.
          var vGutterShare = (rowCount - 1) / rowCount;

          // Percent of the available vertical space that one row takes up.
          vShare = (1 / rowCount) * 100;

          // Base vertical size of a row.
          vUnit = UNIT({share: vShare, gutterShare: vGutterShare, gutter: gutter});

          style.top = POSITION({unit: vUnit, offset: position.row, gutter: gutter});
          style.height = DIMENSION({unit: vUnit, span: spans.row, gutter: gutter});
          break;
      }

      return style;
    }

    function getGridStyle(colCount, rowCount, gutter, rowMode, rowHeight) {
      var style = {};

      switch (rowMode) {
        case 'fixed':
          style.height = DIMENSION({ unit: rowHeight, span: rowCount, gutter: gutter });
          style.paddingBottom = '';
          break;

        case 'ratio':
          // rowHeight is width / height
          var hGutterShare = colCount === 1 ? 0 : (colCount - 1) / colCount,
              hShare = (1 / colCount) * 100,
              vShare = hShare * (1 / rowHeight),
              vUnit = UNIT({ share: vShare, gutterShare: hGutterShare, gutter: gutter });

          style.height = '';
          style.paddingBottom = DIMENSION({ unit: vUnit, span: rowCount, gutter: gutter});
          break;

        case 'fit':
          // noop, as the height is user set
          break;
      }

      return style;
    }

    function getTileElements() {
      return [].filter.call(element.children(), function(ele) {
        return ele.tagName == 'MD-GRID-TILE' && !ele.$$mdDestroyed;
      });
    }

    /**
     * Gets an array of objects containing the rowspan and colspan for each tile.
     * @returns {Array<{row: number, col: number}>}
     */
    function getTileSpans(tileElements) {
      return [].map.call(tileElements, function(ele) {
        var ctrl = angular.element(ele).controller('mdGridTile');
        return {
          row: parseInt(
              $mdMedia.getResponsiveAttribute(ctrl.$attrs, 'md-rowspan'), 10) || 1,
          col: parseInt(
              $mdMedia.getResponsiveAttribute(ctrl.$attrs, 'md-colspan'), 10) || 1
        };
      });
    }

    function getColumnCount() {
      var colCount = parseInt($mdMedia.getResponsiveAttribute(attrs, 'md-cols'), 10);
      if (isNaN(colCount)) {
        throw 'md-grid-list: md-cols attribute was not found, or contained a non-numeric value';
      }
      return colCount;
    }

    function getGutter() {
      return applyDefaultUnit($mdMedia.getResponsiveAttribute(attrs, 'md-gutter') || 1);
    }

    function getRowHeight() {
      var rowHeight = $mdMedia.getResponsiveAttribute(attrs, 'md-row-height');
      if (!rowHeight) {
        throw 'md-grid-list: md-row-height attribute was not found';
      }

      switch (getRowMode()) {
        case 'fixed':
          return applyDefaultUnit(rowHeight);
        case 'ratio':
          var whRatio = rowHeight.split(':');
          return parseFloat(whRatio[0]) / parseFloat(whRatio[1]);
        case 'fit':
          return 0; // N/A
      }
    }

    function getRowMode() {
      var rowHeight = $mdMedia.getResponsiveAttribute(attrs, 'md-row-height');
      if (!rowHeight) {
        throw 'md-grid-list: md-row-height attribute was not found';
      }

      if (rowHeight == 'fit') {
        return 'fit';
      } else if (rowHeight.indexOf(':') !== -1) {
        return 'ratio';
      } else {
        return 'fixed';
      }
    }

    function applyDefaultUnit(val) {
      return /\D$/.test(val) ? val : val + 'px';
    }
  }
}

/* @ngInject */
function GridListController($mdUtil) {
  this.layoutInvalidated = false;
  this.tilesInvalidated = false;
  this.$timeout_ = $mdUtil.nextTick;
  this.layoutDelegate = angular.noop;
}

GridListController.prototype = {
  invalidateTiles: function() {
    this.tilesInvalidated = true;
    this.invalidateLayout();
  },

  invalidateLayout: function() {
    if (this.layoutInvalidated) {
      return;
    }
    this.layoutInvalidated = true;
    this.$timeout_(angular.bind(this, this.layout));
  },

  layout: function() {
    try {
      this.layoutDelegate(this.tilesInvalidated);
    } finally {
      this.layoutInvalidated = false;
      this.tilesInvalidated = false;
    }
  }
};


/* @ngInject */
function GridLayoutFactory($mdUtil) {
  var defaultAnimator = GridTileAnimator;

  /**
   * Set the reflow animator callback
   */
  GridLayout.animateWith = function(customAnimator) {
    defaultAnimator = !angular.isFunction(customAnimator) ? GridTileAnimator : customAnimator;
  };

  return GridLayout;

  /**
   * Publish layout function
   */
  function GridLayout(colCount, tileSpans) {
      var self, layoutInfo, gridStyles, layoutTime, mapTime, reflowTime;

      layoutTime = $mdUtil.time(function() {
        layoutInfo = calculateGridFor(colCount, tileSpans);
      });

      return self = {

        /**
         * An array of objects describing each tile's position in the grid.
         */
        layoutInfo: function() {
          return layoutInfo;
        },

        /**
         * Maps grid positioning to an element and a set of styles using the
         * provided updateFn.
         */
        map: function(updateFn) {
          mapTime = $mdUtil.time(function() {
            var info = self.layoutInfo();
            gridStyles = updateFn(info.positioning, info.rowCount);
          });
          return self;
        },

        /**
         * Default animator simply sets the element.css( <styles> ). An alternate
         * animator can be provided as an argument. The function has the following
         * signature:
         *
         *    function({grid: {element: JQLite, style: Object}, tiles: Array<{element: JQLite, style: Object}>)
         */
        reflow: function(animatorFn) {
          reflowTime = $mdUtil.time(function() {
            var animator = animatorFn || defaultAnimator;
            animator(gridStyles.grid, gridStyles.tiles);
          });
          return self;
        },

        /**
         * Timing for the most recent layout run.
         */
        performance: function() {
          return {
            tileCount: tileSpans.length,
            layoutTime: layoutTime,
            mapTime: mapTime,
            reflowTime: reflowTime,
            totalTime: layoutTime + mapTime + reflowTime
          };
        }
      };
    }

  /**
   * Default Gridlist animator simple sets the css for each element;
   * NOTE: any transitions effects must be manually set in the CSS.
   * e.g.
   *
   *  md-grid-tile {
   *    transition: all 700ms ease-out 50ms;
   *  }
   *
   */
  function GridTileAnimator(grid, tiles) {
    grid.element.css(grid.style);
    tiles.forEach(function(t) {
      t.element.css(t.style);
    });
  }

  /**
   * Calculates the positions of tiles.
   *
   * The algorithm works as follows:
   *    An Array<Number> with length colCount (spaceTracker) keeps track of
   *    available tiling positions, where elements of value 0 represents an
   *    empty position. Space for a tile is reserved by finding a sequence of
   *    0s with length <= than the tile's colspan. When such a space has been
   *    found, the occupied tile positions are incremented by the tile's
   *    rowspan value, as these positions have become unavailable for that
   *    many rows.
   *
   *    If the end of a row has been reached without finding space for the
   *    tile, spaceTracker's elements are each decremented by 1 to a minimum
   *    of 0. Rows are searched in this fashion until space is found.
   */
  function calculateGridFor(colCount, tileSpans) {
    var curCol = 0,
        curRow = 0,
        spaceTracker = newSpaceTracker();

    return {
      positioning: tileSpans.map(function(spans, i) {
        return {
          spans: spans,
          position: reserveSpace(spans, i)
        };
      }),
      rowCount: curRow + Math.max.apply(Math, spaceTracker)
    };

    function reserveSpace(spans, i) {
      if (spans.col > colCount) {
        throw 'md-grid-list: Tile at position ' + i + ' has a colspan ' +
            '(' + spans.col + ') that exceeds the column count ' +
            '(' + colCount + ')';
      }

      var start = 0,
          end = 0;

      // TODO(shyndman): This loop isn't strictly necessary if you can
      // determine the minimum number of rows before a space opens up. To do
      // this, recognize that you've iterated across an entire row looking for
      // space, and if so fast-forward by the minimum rowSpan count. Repeat
      // until the required space opens up.
      while (end - start < spans.col) {
        if (curCol >= colCount) {
          nextRow();
          continue;
        }

        start = spaceTracker.indexOf(0, curCol);
        if (start === -1 || (end = findEnd(start + 1)) === -1) {
          start = end = 0;
          nextRow();
          continue;
        }

        curCol = end + 1;
      }

      adjustRow(start, spans.col, spans.row);
      curCol = start + spans.col;

      return {
        col: start,
        row: curRow
      };
    }

    function nextRow() {
      curCol = 0;
      curRow++;
      adjustRow(0, colCount, -1); // Decrement row spans by one
    }

    function adjustRow(from, cols, by) {
      for (var i = from; i < from + cols; i++) {
        spaceTracker[i] = Math.max(spaceTracker[i] + by, 0);
      }
    }

    function findEnd(start) {
      var i;
      for (i = start; i < spaceTracker.length; i++) {
        if (spaceTracker[i] !== 0) {
          return i;
        }
      }

      if (i === spaceTracker.length) {
        return i;
      }
    }

    function newSpaceTracker() {
      var tracker = [];
      for (var i = 0; i < colCount; i++) {
        tracker.push(0);
      }
      return tracker;
    }
  }
}

/**
 * @ngdoc directive
 * @name mdGridTile
 * @module material.components.gridList
 * @restrict E
 * @description
 * Tiles contain the content of an `md-grid-list`. They span one or more grid
 * cells vertically or horizontally, and use `md-grid-tile-{footer,header}` to
 * display secondary content.
 *
 * ### Responsive Attributes
 *
 * The `md-grid-tile` directive supports "responsive" attributes, which allow
 * different `md-rowspan` and `md-colspan` values depending on the currently
 * matching media query.
 *
 * In order to set a responsive attribute, first define the fallback value with
 * the standard attribute name, then add additional attributes with the
 * following convention: `{base-attribute-name}-{media-query-name}="{value}"`
 * (ie. `md-colspan-sm="4"`)
 *
 * @param {number=} md-colspan The number of columns to span (default 1). Cannot
 *    exceed the number of columns in the grid. Supports interpolation.
 * @param {number=} md-rowspan The number of rows to span (default 1). Supports
 *     interpolation.
 *
 * @usage
 * With header:
 * <hljs lang="html">
 * <md-grid-tile>
 *   <md-grid-tile-header>
 *     <h3>This is a header</h3>
 *   </md-grid-tile-header>
 * </md-grid-tile>
 * </hljs>
 *
 * With footer:
 * <hljs lang="html">
 * <md-grid-tile>
 *   <md-grid-tile-footer>
 *     <h3>This is a footer</h3>
 *   </md-grid-tile-footer>
 * </md-grid-tile>
 * </hljs>
 *
 * Spanning multiple rows/columns:
 * <hljs lang="html">
 * <md-grid-tile md-colspan="2" md-rowspan="3">
 * </md-grid-tile>
 * </hljs>
 *
 * Responsive attributes:
 * <hljs lang="html">
 * <md-grid-tile md-colspan="1" md-colspan-sm="3" md-colspan-md="5">
 * </md-grid-tile>
 * </hljs>
 */
function GridTileDirective($mdMedia) {
  return {
    restrict: 'E',
    require: '^mdGridList',
    template: '<figure ng-transclude></figure>',
    transclude: true,
    scope: {},
    // Simple controller that exposes attributes to the grid directive
    controller: ["$attrs", function($attrs) {
      this.$attrs = $attrs;
    }],
    link: postLink
  };

  function postLink(scope, element, attrs, gridCtrl) {
    // Apply semantics
    element.attr('role', 'listitem');

    // If our colspan or rowspan changes, trigger a layout
    var unwatchAttrs = $mdMedia.watchResponsiveAttributes(['md-colspan', 'md-rowspan'],
        attrs, angular.bind(gridCtrl, gridCtrl.invalidateLayout));

    // Tile registration/deregistration
    gridCtrl.invalidateTiles();
    scope.$on('$destroy', function() {
      // Mark the tile as destroyed so it is no longer considered in layout,
      // even if the DOM element sticks around (like during a leave animation)
      element[0].$$mdDestroyed = true;
      unwatchAttrs();
      gridCtrl.invalidateLayout();
    });

    if (angular.isDefined(scope.$parent.$index)) {
      scope.$watch(function() { return scope.$parent.$index; },
        function indexChanged(newIdx, oldIdx) {
          if (newIdx === oldIdx) {
            return;
          }
          gridCtrl.invalidateTiles();
        });
    }
  }
}


function GridTileCaptionDirective() {
  return {
    template: '<figcaption ng-transclude></figcaption>',
    transclude: true
  };
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.icon
 * @description
 * Icon
 */
angular.module('material.components.icon', ['material.core']);

})();
(function(){
"use strict";

angular
  .module('material.components.icon')
  .directive('mdIcon', ['$mdIcon', '$mdTheming', '$mdAria', '$sce', mdIconDirective]);

/**
 * @ngdoc directive
 * @name mdIcon
 * @module material.components.icon
 *
 * @restrict E
 *
 * @description
 * The `md-icon` directive makes it easier to use vector-based icons in your app (as opposed to
 * raster-based icons types like PNG). The directive supports both icon fonts and SVG icons.
 *
 * Icons should be considered view-only elements that should not be used directly as buttons; instead nest a `<md-icon>`
 * inside a `md-button` to add hover and click features.
 *
 * ### Icon fonts
 * Icon fonts are a technique in which you use a font where the glyphs in the font are
 * your icons instead of text. Benefits include a straightforward way to bundle everything into a
 * single HTTP request, simple scaling, easy color changing, and more.
 *
 * `md-icon` lets you consume an icon font by letting you reference specific icons in that font
 * by name rather than character code.
 *
 * When using font-icons, developers must follow three (3) simple steps:
 *
 * <ol>
 * <li>Load the font library. e.g.<br/>
 *    `<link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">`
 * </li>
 * <li>
 *   Use either (a) font-icon class names or (b) a fontset and a font ligature to render the font glyph by
 *   using its textual name _or_ numerical character reference. Note that `material-icons` is the default fontset when
 *   none is specified.
 * </li>
 * <li> Use any of the following templates: <br/>
 *   <ul>
 *     <li>`<md-icon md-font-icon="classname"></md-icon>`</li>
 *     <li>`<md-icon md-font-set="font library classname or alias">textual_name</md-icon>`</li>
 *     <li>`<md-icon> numerical_character_reference </md-icon>`</li>
 *     <li>`<md-icon ng_bind="'textual_name'"></md-icon>`</li>
 *     <li>`<md-icon ng-bind="scopeVariable"></md-icon>`</li>
 *   </ul>
 * </li>
 * </ol>
 *
 * Full details for these steps can be found in the
 * <a href="http://google.github.io/material-design-icons/#icon-font-for-the-web" target="_blank">
 * Material Design Icon font for the web docs</a>.
 *
 * You can browse and search the Material Design icon style <code>.material-icons</code>
 * in the <a href="https://material.io/tools/icons/" target="_blank">Material Design Icons tool</a>.
 *
 * ### SVG
 * For SVGs, the problem with using `<img>` or a CSS `background-image` is that you can't take
 * advantage of some SVG features, such as styling specific parts of the icon with CSS or SVG
 * animation.
 *
 * `md-icon` makes it easier to use SVG icons by *inlining* the SVG into an `<svg>` element in the
 * document. The most straightforward way of referencing an SVG icon is via URL, just like a
 * traditional `<img>`. `$mdIconProvider`, as a convenience, lets you _name_ an icon so you can
 * reference it by name instead of URL throughout your templates.
 *
 * Additionally, you may not want to make separate HTTP requests for every icon, so you can bundle
 * your SVG icons together and pre-load them with `$mdIconProvider` as an icon set. An icon set can
 * also be given a name, which acts as a namespace for individual icons, so you can reference them
 * like `"social:cake"`.
 *
 * When using SVGs, both external SVGs (via URLs) or sets of SVGs (from icon sets) can be
 * easily loaded and used.
 *
 * ### Localization
 *
 * Because an `md-icon` element's text content is not intended to be translated, it is recommended
 * to declare the text content for an `md-icon` element in its start tag. Instead of using the HTML
 * text content, consider using `ng-bind` with a scope variable or literal string.
 *
 * Examples:
 *
 * <ul>
 *   <li>`<md-icon ng-bind="myIconVariable"></md-icon>`</li>
 *   <li>`<md-icon ng-bind="'menu'"></md-icon>`
 * </ul>
 *
 * <h2 id="material_design_icons">Material Design Icons tool</h2>
 * Using the Material Design Icons tool, developers can easily and quickly search for a specific
 * open source Material Design icon. The search is in the top left. Below search, you can select
 * from the new icon themes or filter by icon category.
 *
 * <a href="https://material.io/tools/icons/" target="_blank" style="border-bottom:none;">
 * <img src="https://user-images.githubusercontent.com/3506071/41942584-ef0695d0-796d-11e8-9436-44f25023a111.png"
 *      aria-label="Material Design Icons tool" style="max-width:95%">
 * </a>
 *
 * <div class="md-caption" style="text-align: center; margin-bottom: 24px">
 *  Click on the image above to open the
 *  <a href="https://material.io/tools/icons/" target="_blank">Material Design Icons tool</a>.
 * </div>
 *
 * Click on any icon, then click on the "Selected Icon" chevron to see the slide-up
 * information panel with details regarding a SVG download and information on the font-icon's
 * textual name. This panel also allows you to select a black on transparent or white on transparent
 * icon and to change the icon size. These settings only affect the downloaded icons.
 *
 * @param {string} md-font-icon String name of CSS icon associated with the font-face will be used
 * to render the icon. Requires the fonts and the named CSS styles to be preloaded.
 * @param {string} md-font-set CSS style name associated with the font library; which will be assigned as
 * the class for the font-icon ligature. This value may also be an alias that is used to lookup the classname;
 * internally use `$mdIconProvider.fontSet(<alias>)` to determine the style name.
 * @param {string} md-svg-src String URL (or expression) used to load, cache, and display an
 *     external SVG.
 * @param {string} md-svg-icon md-svg-icon String name used for lookup of the icon from the internal cache;
 *     interpolated strings or expressions may also be used. Specific set names can be used with
 *     the syntax `<set name>:<icon name>`.<br/><br/>
 * To use icon sets, developers are required to pre-register the sets using the `$mdIconProvider` service.
 * @param {string=} aria-label Labels icon for accessibility. If an empty string is provided, icon
 * will be hidden from accessibility layer with `aria-hidden="true"`. If there's no aria-label on the icon
 * nor a label on the parent element, a warning will be logged to the console.
 * @param {string=} alt Labels icon for accessibility. If an empty string is provided, icon
 * will be hidden from accessibility layer with `aria-hidden="true"`. If there's no alt on the icon
 * nor a label on the parent element, a warning will be logged to the console.
 *
 * @usage
 * When using SVGs:
 * <hljs lang="html">
 *
 *<!-- Icon ID; may contain optional icon set prefix.
 *     Icons must be registered using $mdIconProvider. -->
 *<md-icon md-svg-icon="social:android"    aria-label="android " ></md-icon>
 *
 *<!-- Icon urls; may be preloaded in templateCache -->
 *<md-icon md-svg-src="/android.svg"       aria-label="android " ></md-icon>
 *<md-icon md-svg-src="{{ getAndroid() }}" aria-label="android " ></md-icon>
 *
 * </hljs>
 *
 * Use the <code>$mdIconProvider</code> to configure your application with
 * SVG icon sets.
 *
 * <hljs lang="js">
 * angular.module('appSvgIconSets', ['ngMaterial'])
 *   .controller('DemoCtrl', function($scope) {})
 *   .config(function($mdIconProvider) {
 *     $mdIconProvider
 *       .iconSet('social', 'img/icons/sets/social-icons.svg', 24)
 *       .defaultIconSet('img/icons/sets/core-icons.svg', 24);
 *    });
 * </hljs>
 *
 *
 * When using Font Icons with classnames:
 * <hljs lang="html">
 *
 * <md-icon md-font-icon="android" aria-label="android" ></md-icon>
 * <md-icon class="icon_home" aria-label="Home"></md-icon>
 *
 * </hljs>
 *
 * When using Material Font Icons with ligatures:
 * <hljs lang="html">
 *  <!--
 *  For Material Design Icons
 *  The class '.material-icons' is auto-added if a style has NOT been specified
 *  since `material-icons` is the default fontset. So your markup:
 *  -->
 *  <md-icon> face </md-icon>
 *  <!-- becomes this at runtime: -->
 *  <md-icon md-font-set="material-icons"> face </md-icon>
 *  <!-- If the fontset does not support ligature names, then we need to use the ligature unicode.-->
 *  <md-icon> &#xE87C; </md-icon>
 *  <!-- The class '.material-icons' must be manually added if other styles are also specified-->
 *  <md-icon class="material-icons md-light md-48"> face </md-icon>
 * </hljs>
 *
 * When using other Font-Icon libraries:
 *
 * <hljs lang="js">
 *  // Specify a font-icon style alias
 *  angular.config(function($mdIconProvider) {
 *    $mdIconProvider.fontSet('md', 'material-icons');
 *  });
 * </hljs>
 *
 * <hljs lang="html">
 *  <md-icon md-font-set="md">favorite</md-icon>
 * </hljs>
 *
 */
function mdIconDirective($mdIcon, $mdTheming, $mdAria, $sce) {

  return {
    restrict: 'E',
    link : postLink
  };


  /**
   * Directive postLink
   * Supports embedded SVGs, font-icons, & external SVGs
   */
  function postLink(scope, element, attr) {
    $mdTheming(element);
    var lastFontIcon = attr.mdFontIcon;
    var lastFontSet = $mdIcon.fontSet(attr.mdFontSet);

    prepareForFontIcon();

    attr.$observe('mdFontIcon', fontIconChanged);
    attr.$observe('mdFontSet', fontIconChanged);

    // Keep track of the content of the svg src so we can compare against it later to see if the
    // attribute is static (and thus safe).
    var originalSvgSrc = element[0].getAttribute(attr.$attr.mdSvgSrc);

    // If using a font-icon, then the textual name of the icon itself
    // provides the aria-label.

    var attrName = attr.$normalize(attr.$attr.mdSvgIcon || attr.$attr.mdSvgSrc || '');

    /* Provide a default accessibility role of img */
    if (!attr.role) {
      $mdAria.expect(element, 'role', 'img');
      /* manually update attr variable */
      attr.role = 'img';
    }

    /* Don't process ARIA if already valid */
    if (attr.role === "img" && !attr.ariaHidden && !$mdAria.hasAriaLabel(element)) {
      var iconName;
      if (attr.alt) {
        /* Use alt text by default if available */
        $mdAria.expect(element, 'aria-label', attr.alt);
      } else if ($mdAria.parentHasAriaLabel(element, 2)) {
        /* Parent has ARIA so we will assume it will describe the image */
        $mdAria.expect(element, 'aria-hidden', 'true');
      } else if (iconName = (attr.mdFontIcon || attr.mdSvgIcon || element.text())) {
        /* Use icon name as aria-label */
        $mdAria.expect(element, 'aria-label', iconName);
      } else {
        /* No label found */
        $mdAria.expect(element, 'aria-hidden', 'true');
      }
    }

    if (attrName) {
      // Use either pre-configured SVG or URL source, respectively.
      attr.$observe(attrName, function(attrVal) {
        element.empty();
        if (attrVal) {
          $mdIcon(attrVal)
            .then(function(svg) {
            element.empty();
            element.append(svg);
          });
        }
      });
    }

    function prepareForFontIcon() {
      if (!attr.mdSvgIcon && !attr.mdSvgSrc) {
        if (attr.mdFontIcon) {
          element.addClass('md-font ' + attr.mdFontIcon);
        }

        element.addClass(lastFontSet);
      }
    }

    function fontIconChanged() {
      if (!attr.mdSvgIcon && !attr.mdSvgSrc) {
        if (attr.mdFontIcon) {
          element.removeClass(lastFontIcon);
          element.addClass(attr.mdFontIcon);

          lastFontIcon = attr.mdFontIcon;
        }

        var fontSet = $mdIcon.fontSet(attr.mdFontSet);

        if (lastFontSet !== fontSet) {
          element.removeClass(lastFontSet);
          element.addClass(fontSet);

          lastFontSet = fontSet;
        }
      }
    }
  }
}

})();
(function(){
"use strict";

  
MdIconService.$inject = ["config", "$templateRequest", "$q", "$log", "$mdUtil", "$sce"];angular
    .module('material.components.icon')
    .constant('$$mdSvgRegistry', {
        'mdTabsArrow':   'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxnPjxwb2x5Z29uIHBvaW50cz0iMTUuNCw3LjQgMTQsNiA4LDEyIDE0LDE4IDE1LjQsMTYuNiAxMC44LDEyICIvPjwvZz48L3N2Zz4=',
        'mdClose':       'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxnPjxwYXRoIGQ9Ik0xOSA2LjQxbC0xLjQxLTEuNDEtNS41OSA1LjU5LTUuNTktNS41OS0xLjQxIDEuNDEgNS41OSA1LjU5LTUuNTkgNS41OSAxLjQxIDEuNDEgNS41OS01LjU5IDUuNTkgNS41OSAxLjQxLTEuNDEtNS41OS01LjU5eiIvPjwvZz48L3N2Zz4=',
        'mdCancel':      'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxnPjxwYXRoIGQ9Ik0xMiAyYy01LjUzIDAtMTAgNC40Ny0xMCAxMHM0LjQ3IDEwIDEwIDEwIDEwLTQuNDcgMTAtMTAtNC40Ny0xMC0xMC0xMHptNSAxMy41OWwtMS40MSAxLjQxLTMuNTktMy41OS0zLjU5IDMuNTktMS40MS0xLjQxIDMuNTktMy41OS0zLjU5LTMuNTkgMS40MS0xLjQxIDMuNTkgMy41OSAzLjU5LTMuNTkgMS40MSAxLjQxLTMuNTkgMy41OSAzLjU5IDMuNTl6Ii8+PC9nPjwvc3ZnPg==',
        'mdMenu':        'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxwYXRoIGQ9Ik0zLDZIMjFWOEgzVjZNMywxMUgyMVYxM0gzVjExTTMsMTZIMjFWMThIM1YxNloiIC8+PC9zdmc+',
        'mdToggleArrow': 'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgNDggNDgiPjxwYXRoIGQ9Ik0yNCAxNmwtMTIgMTIgMi44MyAyLjgzIDkuMTctOS4xNyA5LjE3IDkuMTcgMi44My0yLjgzeiIvPjxwYXRoIGQ9Ik0wIDBoNDh2NDhoLTQ4eiIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==',
        'mdCalendar':    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNMTkgM2gtMVYxaC0ydjJIOFYxSDZ2Mkg1Yy0xLjExIDAtMS45OS45LTEuOTkgMkwzIDE5YzAgMS4xLjg5IDIgMiAyaDE0YzEuMSAwIDItLjkgMi0yVjVjMC0xLjEtLjktMi0yLTJ6bTAgMTZINVY4aDE0djExek03IDEwaDV2NUg3eiIvPjwvc3ZnPg==',
        'mdChecked':     'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxnPjxwYXRoIGQ9Ik05IDE2LjE3TDQuODMgMTJsLTEuNDIgMS40MUw5IDE5IDIxIDdsLTEuNDEtMS40MXoiLz48L2c+PC9zdmc+'
    })
    .provider('$mdIcon', MdIconProvider);

/**
 * @ngdoc service
 * @name $mdIconProvider
 * @module material.components.icon
 *
 * @description
 * `$mdIconProvider` is used only to register icon IDs with URLs. These configuration features allow
 * icons and icon sets to be pre-registered and associated with source URLs **before** the `<md-icon />`
 * directives are compiled.
 *
 * If using font-icons, the developer is responsible for loading the fonts.
 *
 * If using SVGs, loading of the actual svg files are deferred to on-demand requests and are loaded
 * internally by the `$mdIcon` service using the `$templateRequest` service. When an SVG is
 * requested by name/ID, the `$mdIcon` service searches its registry for the associated source URL;
 * that URL is used to on-demand load and parse the SVG dynamically.
 *
 * The `$templateRequest` service expects the icons source to be loaded over trusted URLs.<br/>
 * This means, when loading icons from an external URL, you have to trust the URL in the `$sceDelegateProvider`.
 *
 * <hljs lang="js">
 *   app.config(function($sceDelegateProvider) {
 *     $sceDelegateProvider.resourceUrlWhitelist([
 *       // Adding 'self' to the whitelist, will allow requests from the current origin.
 *       'self',
 *       // Using double asterisks here, will allow all URLs to load.
 *       // We recommend to only specify the given domain you want to allow.
 *       '**'
 *     ]);
 *   });
 * </hljs>
 *
 * Read more about the [$sceDelegateProvider](https://docs.angularjs.org/api/ng/provider/$sceDelegateProvider).
 *
 * **Notice:** Most font-icons libraries do not support ligatures (for example `fontawesome`).<br/>
 *  In such cases you are not able to use the icon's ligature name - Like so:
 *
 *  <hljs lang="html">
 *    <md-icon md-font-set="fa">fa-bell</md-icon>
 *  </hljs>
 *
 * You should instead use the given unicode, instead of the ligature name.
 *
 * <p ng-hide="true"> ##// Notice we can't use a hljs element here, because the characters will be escaped.</p>
 *  ```html
 *    <md-icon md-font-set="fa">&#xf0f3</md-icon>
 *  ```
 *
 * All unicode ligatures are prefixed with the `&#x` string.
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Configure URLs for icons specified by [set:]id.
    *
    *     $mdIconProvider
    *          .defaultFontSet( 'fa' )                   // This sets our default fontset className.
    *          .defaultIconSet('my/app/icons.svg')       // Register a default set of SVG icons
    *          .iconSet('social', 'my/app/social.svg')   // Register a named icon set of SVGs
    *          .icon('android', 'my/app/android.svg')    // Register a specific icon (by name)
    *          .icon('work:chair', 'my/app/chair.svg');  // Register icon in a specific set
    *   });
 * </hljs>
 *
 * SVG icons and icon sets can be easily pre-loaded and cached using either (a) a build process or (b) a runtime
 * **startup** process (shown below):
 *
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Register a default set of SVG icon definitions
    *     $mdIconProvider.defaultIconSet('my/app/icons.svg')
    *
    *   })
 *   .run(function($templateRequest){
    *
    *     // Pre-fetch icons sources by URL and cache in the $templateCache...
    *     // subsequent $templateRequest calls will look there first.
    *
    *     var urls = [ 'imy/app/icons.svg', 'img/icons/android.svg'];
    *
    *     angular.forEach(urls, function(url) {
    *       $templateRequest(url);
    *     });
    *
    *   });
 *
 * </hljs>
 *
 * > <b>Note:</b> The loaded SVG data is subsequently cached internally for future requests.
 *
 */

/**
 * @ngdoc method
 * @name $mdIconProvider#icon
 *
 * @description
 * Register a source URL for a specific icon name; the name may include optional 'icon set' name prefix.
 * These icons  will later be retrieved from the cache using `$mdIcon( <icon name> )`
 *
 * @param {string} id Icon name/id used to register the icon
 * @param {string} url specifies the external location for the data file. Used internally by
 * `$templateRequest` to load the data or as part of the lookup in `$templateCache` if pre-loading
 * was configured.
 * @param {number=} viewBoxSize Sets the width and height the icon's viewBox.
 * It is ignored for icons with an existing viewBox. Default size is 24.
 *
 * @returns {obj} an `$mdIconProvider` reference; used to support method call chains for the API
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Configure URLs for icons specified by [set:]id.
    *
    *     $mdIconProvider
    *          .icon('android', 'my/app/android.svg')    // Register a specific icon (by name)
    *          .icon('work:chair', 'my/app/chair.svg');  // Register icon in a specific set
    *   });
 * </hljs>
 *
 */
/**
 * @ngdoc method
 * @name $mdIconProvider#iconSet
 *
 * @description
 * Register a source URL for a 'named' set of icons; group of SVG definitions where each definition
 * has an icon id. Individual icons can be subsequently retrieved from this cached set using
 * `$mdIcon(<icon set name>:<icon name>)`
 *
 * @param {string} id Icon name/id used to register the iconset
 * @param {string} url specifies the external location for the data file. Used internally by
 * `$templateRequest` to load the data or as part of the lookup in `$templateCache` if pre-loading
 * was configured.
 * @param {number=} viewBoxSize Sets the width and height of the viewBox of all icons in the set.
 * It is ignored for icons with an existing viewBox. All icons in the icon set should be the same size.
 * Default value is 24.
 *
 * @returns {obj} an `$mdIconProvider` reference; used to support method call chains for the API
 *
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Configure URLs for icons specified by [set:]id.
    *
    *     $mdIconProvider
    *          .iconSet('social', 'my/app/social.svg')   // Register a named icon set
    *   });
 * </hljs>
 *
 */
/**
 * @ngdoc method
 * @name $mdIconProvider#defaultIconSet
 *
 * @description
 * Register a source URL for the default 'named' set of icons. Unless explicitly registered,
 * subsequent lookups of icons will failover to search this 'default' icon set.
 * Icon can be retrieved from this cached, default set using `$mdIcon(<name>)`
 *
 * @param {string} url specifies the external location for the data file. Used internally by
 * `$templateRequest` to load the data or as part of the lookup in `$templateCache` if pre-loading
 * was configured.
 * @param {number=} viewBoxSize Sets the width and height of the viewBox of all icons in the set.
 * It is ignored for icons with an existing viewBox. All icons in the icon set should be the same size.
 * Default value is 24.
 *
 * @returns {obj} an `$mdIconProvider` reference; used to support method call chains for the API
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Configure URLs for icons specified by [set:]id.
    *
    *     $mdIconProvider
    *          .defaultIconSet( 'my/app/social.svg' )   // Register a default icon set
    *   });
 * </hljs>
 *
 */
/**
 * @ngdoc method
 * @name $mdIconProvider#defaultFontSet
 *
 * @description
 * When using Font-Icons, AngularJS Material assumes the the Material Design icons will be used and automatically
 * configures the default font-set == 'material-icons'. Note that the font-set references the font-icon library
 * class style that should be applied to the `<md-icon>`.
 *
 * Configuring the default means that the attributes
 * `md-font-set="material-icons"` or `class="material-icons"` do not need to be explicitly declared on the
 * `<md-icon>` markup. For example:
 *
 *  `<md-icon> face </md-icon>`
 *  will render as
 *  `<span class="material-icons"> face </span>`, and
 *
 *  `<md-icon md-font-set="fa"> face </md-icon>`
 *  will render as
 *  `<span class="fa"> face </span>`
 *
 * @param {string} name of the font-library style that should be applied to the md-icon DOM element
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
   *     $mdIconProvider.defaultFontSet( 'fa' );
   *   });
 * </hljs>
 *
 */

/**
 * @ngdoc method
 * @name $mdIconProvider#fontSet
 *
 * @description
 * When using a font set for `<md-icon>` you must specify the correct font classname in the `md-font-set`
 * attribute. If the fonset className is really long, your markup may become cluttered... an easy
 * solution is to define an `alias` for your fontset:
 *
 * @param {string} alias of the specified fontset.
 * @param {string} className of the fontset.
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
   *     // In this case, we set an alias for the `material-icons` fontset.
   *     $mdIconProvider.fontSet('md', 'material-icons');
   *   });
 * </hljs>
 *
 */

/**
 * @ngdoc method
 * @name $mdIconProvider#defaultViewBoxSize
 *
 * @description
 * While `<md-icon />` markup can also be style with sizing CSS, this method configures
 * the default width **and** height used for all icons; unless overridden by specific CSS.
 * The default sizing is (24px, 24px).
 * @param {number=} viewBoxSize Sets the width and height of the viewBox for an icon or an icon set.
 * All icons in a set should be the same size. The default value is 24.
 *
 * @returns {obj} an `$mdIconProvider` reference; used to support method call chains for the API
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Configure URLs for icons specified by [set:]id.
    *
    *     $mdIconProvider
    *          .defaultViewBoxSize(36)   // Register a default icon size (width == height)
    *   });
 * </hljs>
 *
 */

var config = {
  defaultViewBoxSize: 24,
  defaultFontSet: 'material-icons',
  fontSets: []
};

function MdIconProvider() {
}

MdIconProvider.prototype = {
  icon: function(id, url, viewBoxSize) {
    if (id.indexOf(':') == -1) id = '$default:' + id;

    config[id] = new ConfigurationItem(url, viewBoxSize);
    return this;
  },

  iconSet: function(id, url, viewBoxSize) {
    config[id] = new ConfigurationItem(url, viewBoxSize);
    return this;
  },

  defaultIconSet: function(url, viewBoxSize) {
    var setName = '$default';

    if (!config[setName]) {
      config[setName] = new ConfigurationItem(url, viewBoxSize);
    }

    config[setName].viewBoxSize = viewBoxSize || config.defaultViewBoxSize;

    return this;
  },

  defaultViewBoxSize: function(viewBoxSize) {
    config.defaultViewBoxSize = viewBoxSize;
    return this;
  },

  /**
   * Register an alias name associated with a font-icon library style ;
   */
  fontSet: function fontSet(alias, className) {
    config.fontSets.push({
      alias: alias,
      fontSet: className || alias
    });
    return this;
  },

  /**
   * Specify a default style name associated with a font-icon library
   * fallback to Material Icons.
   *
   */
  defaultFontSet: function defaultFontSet(className) {
    config.defaultFontSet = !className ? '' : className;
    return this;
  },

  defaultIconSize: function defaultIconSize(iconSize) {
    config.defaultIconSize = iconSize;
    return this;
  },

  $get: ['$templateRequest', '$q', '$log', '$mdUtil', '$sce', function($templateRequest, $q, $log, $mdUtil, $sce) {
    return MdIconService(config, $templateRequest, $q, $log, $mdUtil, $sce);
  }]
};

  /**
   * Configuration item stored in the Icon registry; used for lookups
   * to load if not already cached in the `loaded` cache
   * @param {string} url
   * @param {=number} viewBoxSize
   * @constructor
   */
  function ConfigurationItem(url, viewBoxSize) {
    this.url = url;
    this.viewBoxSize = viewBoxSize || config.defaultViewBoxSize;
  }

/**
 * @ngdoc service
 * @name $mdIcon
 * @module material.components.icon
 *
 * @description
 * The `$mdIcon` service is a function used to lookup SVG icons.
 *
 * @param {string} id Query value for a unique Id or URL. If the argument is a URL, then the service will retrieve the icon element
 * from its internal cache or load the icon and cache it first. If the value is not a URL-type string, then an ID lookup is
 * performed. The Id may be a unique icon ID or may include an iconSet ID prefix.
 *
 * For the **id** query to work properly, this means that all id-to-URL mappings must have been previously configured
 * using the `$mdIconProvider`.
 *
 * @returns {angular.$q.Promise} A promise that gets resolved to a clone of the initial SVG DOM element; which was
 * created from the SVG markup in the SVG data file. If an error occurs (e.g. the icon cannot be found) the promise
 * will get rejected.
 *
 * @usage
 * <hljs lang="js">
 * function SomeDirective($mdIcon) {
  *
  *   // See if the icon has already been loaded, if not
  *   // then lookup the icon from the registry cache, load and cache
  *   // it for future requests.
  *   // NOTE: ID queries require configuration with $mdIconProvider
  *
  *   $mdIcon('android').then(function(iconEl)    { element.append(iconEl); });
  *   $mdIcon('work:chair').then(function(iconEl) { element.append(iconEl); });
  *
  *   // Load and cache the external SVG using a URL
  *
  *   $mdIcon('img/icons/android.svg').then(function(iconEl) {
  *     element.append(iconEl);
  *   });
  * };
 * </hljs>
 *
 * > <b>Note:</b> The `<md-icon>` directive internally uses the `$mdIcon` service to query, load,
 *   and instantiate SVG DOM elements.
 */

/* @ngInject */
function MdIconService(config, $templateRequest, $q, $log, $mdUtil, $sce) {
  var iconCache = {};
  var svgCache = {};
  var urlRegex = /[-\w@:%+.~#?&//=]{2,}\.[a-z]{2,4}\b(\/[-\w@:%+.~#?&//=]*)?/i;
  var dataUrlRegex = /^data:image\/svg\+xml[\s*;\w\-=]*?(base64)?,(.*)$/i;

  Icon.prototype = {clone: cloneSVG, prepare: prepareAndStyle};
  getIcon.fontSet = findRegisteredFontSet;

  // Publish service...
  return getIcon;

  /**
   * Actual $mdIcon service is essentially a lookup function
   * @param {*} id $sce trust wrapper over a URL string, URL, icon registry id, or icon set id
   * @returns {angular.$q.Promise}
   */
  function getIcon(id) {
    id = id || '';

    // If the "id" provided is not a string, the only other valid value is a $sce trust wrapper
    // over a URL string. If the value is not trusted, this will intentionally throw an error
    // because the user is attempted to use an unsafe URL, potentially opening themselves up
    // to an XSS attack.
    if (!angular.isString(id)) {
      id = $sce.getTrustedUrl(id);
    }

    // If already loaded and cached, use a clone of the cached icon.
    // Otherwise either load by URL, or lookup in the registry and then load by URL, and cache.

    if (iconCache[id]) {
      return $q.when(transformClone(iconCache[id]));
    }

    if (urlRegex.test(id) || dataUrlRegex.test(id)) {
      return loadByURL(id).then(cacheIcon(id));
    }

    if (id.indexOf(':') === -1) {
      id = '$default:' + id;
    }

    var load = config[id] ? loadByID : loadFromIconSet;
    return load(id)
      .then(cacheIcon(id));
  }

  /**
   * Lookup a registered fontSet style using its alias.
   * @param {string} alias used to lookup the alias in the array of fontSets
   * @returns {*} matching fontSet or the defaultFontSet if that alias does not match
   */
  function findRegisteredFontSet(alias) {
    var useDefault = angular.isUndefined(alias) || !(alias && alias.length);
    if (useDefault) {
      return config.defaultFontSet;
    }

    var result = alias;
    angular.forEach(config.fontSets, function(fontSet) {
      if (fontSet.alias === alias) {
        result = fontSet.fontSet || result;
      }
    });

    return result;
  }

  /**
   * @param {!Icon} cacheElement cached icon from the iconCache
   * @returns {Icon} cloned Icon element with unique ids
   */
  function transformClone(cacheElement) {
    var clone = cacheElement.clone();
    var newUid = $mdUtil.nextUid();
    var cacheSuffix, svgUrlQuerySelector, i, xlinkHrefValue;
    // These are SVG attributes that can reference element ids.
    var svgUrlAttributes = [
      'clip-path', 'color-profile', 'cursor', 'fill', 'filter', 'href', 'marker-start',
      'marker-mid', 'marker-end', 'mask', 'stroke', 'style', 'vector-effect'
    ];
    var isIeSvg = clone.innerHTML === undefined;

    // Verify that the newUid only contains a number and not some XSS content.
    if (!isFinite(Number(newUid))) {
      throw new Error('Unsafe and unexpected non-number result from $mdUtil.nextUid().');
    }
    cacheSuffix = '_cache' + newUid;

    // For each cached icon, we need to modify the id attributes and references.
    // This is needed because SVG ids are treated as normal DOM ids and should not be duplicated on
    // the page.
    if (clone.id) {
      clone.id += cacheSuffix;
    }

    // Do as much as possible with querySelectorAll as it provides much greater performance
    // than RegEx against serialized DOM.
    angular.forEach(clone.querySelectorAll('[id]'), function(descendantElem) {
      svgUrlQuerySelector = '';
      for (i = 0; i < svgUrlAttributes.length; i++) {
        svgUrlQuerySelector += '[' + svgUrlAttributes[i] + '="url(#' + descendantElem.id + ')"]';
        if (i + 1 < svgUrlAttributes.length) {
          svgUrlQuerySelector += ', ';
        }
      }
      // Append the cacheSuffix to references of the element's id within url(#id) calls.
      angular.forEach(clone.querySelectorAll(svgUrlQuerySelector), function(refItem) {
        updateSvgIdReferences(descendantElem, refItem, isIeSvg, newUid);
      });
      // Handle usages of url(#id) in the SVG's stylesheets
      angular.forEach(clone.querySelectorAll('style'), function(refItem) {
        updateSvgIdReferences(descendantElem, refItem, isIeSvg, newUid);
      });

      // Update ids referenced by the deprecated (in SVG v2) xlink:href XML attribute. The now
      // preferred href attribute is handled above. However, this non-standard XML namespaced
      // attribute cannot be handled in the same way. Explanation of this query selector here:
      // https://stackoverflow.com/q/23034283/633107.
      angular.forEach(clone.querySelectorAll('[*|href]:not([href])'), function(refItem) {
        xlinkHrefValue = refItem.getAttribute('xlink:href');
        if (xlinkHrefValue) {
          xlinkHrefValue = xlinkHrefValue.replace("#" + descendantElem.id, "#" + descendantElem.id + cacheSuffix);
          refItem.setAttribute('xlink:href', xlinkHrefValue);
        }
      });

      descendantElem.id += cacheSuffix;
    });

    return clone;
  }

  /**
   * @param {Element} referencedElement element w/ id that needs to be updated
   * @param {Element} referencingElement element that references the original id
   * @param {boolean} isIeSvg true if we're dealing with an SVG in IE11, false otherwise
   * @param {string} newUid the cache id to add as part of the cache suffix
   */
  function updateSvgIdReferences(referencedElement, referencingElement, isIeSvg, newUid) {
    var svgElement, cacheSuffix;

    // Verify that the newUid only contains a number and not some XSS content.
    if (!isFinite(Number(newUid))) {
      throw new Error('Unsafe and unexpected non-number result for newUid.');
    }
    cacheSuffix = '_cache' + newUid;

    // outerHTML of SVG elements is not supported by IE11
    if (isIeSvg) {
      svgElement = $mdUtil.getOuterHTML(referencingElement);
      svgElement = svgElement.replace("url(#" + referencedElement.id + ")",
        "url(#" + referencedElement.id + cacheSuffix + ")");
      referencingElement.textContent = angular.element(svgElement)[0].innerHTML;
    } else {
      // This use of outerHTML should be safe from XSS attack since we are only injecting the
      // cacheSuffix with content from $mdUtil.nextUid which we verify is a finite number above.
      referencingElement.outerHTML = referencingElement.outerHTML.replace(
        "url(#" + referencedElement.id + ")",
        "url(#" + referencedElement.id + cacheSuffix + ")");
    }
  }

  /**
   * Prepare and cache the loaded icon for the specified `id`.
   * @param {string} id icon cache id
   * @returns {function(*=): *}
   */
  function cacheIcon(id) {

    return function updateCache(icon) {
      iconCache[id] = isIcon(icon) ? icon : new Icon(icon, config[id]);

      return transformClone(iconCache[id]);
    };
  }

  /**
   * Lookup the configuration in the registry, if !registered throw an error
   * otherwise load the icon [on-demand] using the registered URL.
   * @param {string} id icon registry id
   * @returns {angular.$q.Promise}
   */
  function loadByID(id) {
    var iconConfig = config[id];
    return loadByURL(iconConfig.url).then(function(icon) {
      return new Icon(icon, iconConfig);
    });
  }

  /**
   * Loads the file as XML and uses querySelector( <id> ) to find the desired node...
   * @param {string} id icon id in icon set
   * @returns {angular.$q.Promise}
   */
  function loadFromIconSet(id) {
    var setName = id.substring(0, id.lastIndexOf(':')) || '$default';
    var iconSetConfig = config[setName];

    return !iconSetConfig ? announceIdNotFound(id) : loadByURL(iconSetConfig.url).then(extractFromSet);

    function extractFromSet(set) {
      var iconName = id.slice(id.lastIndexOf(':') + 1);
      var icon = set.querySelector('#' + iconName);
      return icon ? new Icon(icon, iconSetConfig) : announceIdNotFound(id);
    }

    function announceIdNotFound(id) {
      var msg = 'icon ' + id + ' not found';
      $log.warn(msg);

      return $q.reject(msg || id);
    }
  }

  /**
   * Load the icon by URL (may use the $templateCache).
   * Extract the data for later conversion to Icon
   * @param {string} url icon URL
   * @returns {angular.$q.Promise}
   */
  function loadByURL(url) {
    /* Load the icon from embedded data URL. */
    function loadByDataUrl(url) {
      var results = dataUrlRegex.exec(url);
      var isBase64 = /base64/i.test(url);
      var data = isBase64 ? window.atob(results[2]) : results[2];

      return $q.when(angular.element(data)[0]);
    }

    /* Load the icon by URL using HTTP. */
    function loadByHttpUrl(url) {
      return $q(function(resolve, reject) {
        // Catch HTTP or generic errors not related to incorrect icon IDs.
        var announceAndReject = function(err) {
            var msg = angular.isString(err) ? err : (err.message || err.data || err.statusText);
            $log.warn(msg);
            reject(err);
          },
          extractSvg = function(response) {
            if (!svgCache[url]) {
              svgCache[url] = angular.element('<div>').append(response)[0].querySelector('svg');
            }
            resolve(svgCache[url]);
          };

        $templateRequest(url, true).then(extractSvg, announceAndReject);
      });
    }

    return dataUrlRegex.test(url)
      ? loadByDataUrl(url)
      : loadByHttpUrl(url);
  }

  /**
   * Check target signature to see if it is an Icon instance.
   * @param {Icon|Element} target
   * @returns {boolean} true if the specified target is an Icon object, false otherwise.
   */
  function isIcon(target) {
    return angular.isDefined(target.element) && angular.isDefined(target.config);
  }

  /**
   * Define the Icon class
   * @param {Element} el
   * @param {=ConfigurationItem} config
   * @constructor
   */
  function Icon(el, config) {
    // If the node is a <symbol>, it won't be rendered so we have to convert it into <svg>.
    if (el && el.tagName.toLowerCase() === 'symbol') {
      var viewbox = el.getAttribute('viewBox');
      // // Check if innerHTML is supported as IE11 does not support innerHTML on SVG elements.
      if (el.innerHTML) {
        el = angular.element('<svg xmlns="http://www.w3.org/2000/svg">')
          .html(el.innerHTML)[0];
      } else {
        el = angular.element('<svg xmlns="http://www.w3.org/2000/svg">')
          .append($mdUtil.getInnerHTML(el))[0];
      }
      if (viewbox) el.setAttribute('viewBox', viewbox);
    }

    if (el && el.tagName.toLowerCase() !== 'svg') {
      el = angular.element(
        '<svg xmlns="http://www.w3.org/2000/svg">').append(el.cloneNode(true))[0];
    }

    // Inject the namespace if not available...
    if (!el.getAttribute('xmlns')) {
      el.setAttribute('xmlns', "http://www.w3.org/2000/svg");
    }

    this.element = el;
    this.config = config;
    this.prepare();
  }

  /**
   *  Prepare the DOM element that will be cached in the
   *  loaded iconCache store.
   */
  function prepareAndStyle() {
    var viewBoxSize = this.config ? this.config.viewBoxSize : config.defaultViewBoxSize;
    angular.forEach({
      'fit': '',
      'height': '100%',
      'width': '100%',
      'preserveAspectRatio': 'xMidYMid meet',
      'viewBox': this.element.getAttribute('viewBox') || ('0 0 ' + viewBoxSize + ' ' + viewBoxSize),
      'focusable': false // Disable IE11s default behavior to make SVGs focusable
    }, function(val, attr) {
      this.element.setAttribute(attr, val);
    }, this);
  }

  /**
   * Clone the Icon DOM element.
   */
  function cloneSVG() {
    // If the element or any of its children have a style attribute, then a CSP policy without
    // 'unsafe-inline' in the style-src directive, will result in a violation.
    return this.element.cloneNode(true);
  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.input
 */
mdInputContainerDirective.$inject = ["$mdTheming", "$parse", "$$rAF"];
inputTextareaDirective.$inject = ["$mdUtil", "$window", "$mdAria", "$timeout", "$mdGesture"];
mdMaxlengthDirective.$inject = ["$animate", "$mdUtil"];
placeholderDirective.$inject = ["$compile"];
ngMessageDirective.$inject = ["$mdUtil"];
mdSelectOnFocusDirective.$inject = ["$document", "$timeout"];
mdInputInvalidMessagesAnimation.$inject = ["$$AnimateRunner", "$animateCss", "$mdUtil"];
ngMessagesAnimation.$inject = ["$$AnimateRunner", "$animateCss", "$mdUtil"];
ngMessageAnimation.$inject = ["$$AnimateRunner", "$animateCss", "$mdUtil", "$log"];
var inputModule = angular.module('material.components.input', [
    'material.core'
  ])
  .directive('mdInputContainer', mdInputContainerDirective)
  .directive('label', labelDirective)
  .directive('input', inputTextareaDirective)
  .directive('textarea', inputTextareaDirective)
  .directive('mdMaxlength', mdMaxlengthDirective)
  .directive('placeholder', placeholderDirective)
  .directive('ngMessages', ngMessagesDirective)
  .directive('ngMessage', ngMessageDirective)
  .directive('ngMessageExp', ngMessageDirective)
  .directive('mdSelectOnFocus', mdSelectOnFocusDirective)

  .animation('.md-input-invalid', mdInputInvalidMessagesAnimation)
  .animation('.md-input-messages-animation', ngMessagesAnimation)
  .animation('.md-input-message-animation', ngMessageAnimation);

// If we are running inside of tests; expose some extra services so that we can test them
if (window._mdMocksIncluded) {
  inputModule.service('$$mdInput', function() {
    return {
      // special accessor to internals... useful for testing
      messages: {
        getElement  : getMessagesElement
      }
    };
  })

  // Register a service for each animation so that we can easily inject them into unit tests
  .service('mdInputInvalidAnimation', mdInputInvalidMessagesAnimation)
  .service('mdInputMessagesAnimation', ngMessagesAnimation)
  .service('mdInputMessageAnimation', ngMessageAnimation);
}

/**
 * @ngdoc directive
 * @name mdInputContainer
 * @module material.components.input
 *
 * @restrict E
 *
 * @description
 * `<md-input-container>` is the parent of any input or textarea element. It can also optionally
 * wrap `<md-select>` elements so that they will be formatted for use in a form.
 *
 * Input and textarea elements will not behave properly unless the md-input-container parent is
 * provided.
 *
 * A single `<md-input-container>` should contain only one `<input>` or `<md-select>` element,
 * otherwise it will throw an error.
 *
 * <b>Exception:</b> Hidden inputs (`<input type="hidden" />`) are ignored and will not throw an
 * error, so you may combine these with other inputs.
 *
 * <b>Note:</b> When using `ngMessages` with your input element, make sure the message and container
 * elements are *block* elements, otherwise animations applied to the messages will not look as
 * intended. Either use a `div` and apply the `ng-message` and `ng-messages` classes respectively,
 * or use the `md-block` class on your element.
 *
 * @param {expression=} md-is-error When the given expression evaluates to `true`, the input
 *   container will go into the error state. Defaults to erroring if the input has been touched and
 *   is invalid.
 * @param {boolean=} md-no-float When present, `placeholder` attributes on the input will not be
 *   converted to floating labels.
 *
 * @usage
 * <hljs lang="html">
 * <md-input-container>
 *   <label>Username</label>
 *   <input type="text" ng-model="user.name">
 * </md-input-container>
 *
 * <md-input-container>
 *   <label>Description</label>
 *   <textarea ng-model="user.description"></textarea>
 * </md-input-container>
 *
 * <md-input-container>
 *   <md-select ng-model="user.state" placeholder="State of Residence">
 *     <md-option ng-value="state" ng-repeat="state in states">{{ state }}</md-option>
 *   </md-select>
 * </md-input-container>
 * </hljs>
 *
 * <h3>When disabling floating labels</h3>
 * <hljs lang="html">
 *
 * <md-input-container md-no-float>
 *   <input type="text" placeholder="Non-Floating Label">
 * </md-input-container>
 * </hljs>
 */
function mdInputContainerDirective($mdTheming, $parse, $$rAF) {

  ContainerCtrl.$inject = ["$scope", "$element", "$attrs", "$animate"];
  var INPUT_TAGS = ['INPUT', 'TEXTAREA', 'SELECT', 'MD-SELECT'];

  var LEFT_SELECTORS = INPUT_TAGS.reduce(function(selectors, isel) {
    return selectors.concat(['md-icon ~ ' + isel, '.md-icon ~ ' + isel]);
  }, []).join(",");

  var RIGHT_SELECTORS = INPUT_TAGS.reduce(function(selectors, isel) {
    return selectors.concat([isel + ' ~ md-icon', isel + ' ~ .md-icon']);
  }, []).join(",");

  return {
    restrict: 'E',
    compile: compile,
    controller: ContainerCtrl
  };

  function compile(tElement) {
    // Check for both a left & right icon
    var hasLeftIcon = tElement[0].querySelector(LEFT_SELECTORS);
    var hasRightIcon = tElement[0].querySelector(RIGHT_SELECTORS);

    return function postLink(scope, element) {
      $mdTheming(element);

      if (hasLeftIcon || hasRightIcon) {
        // When accessing the element's contents synchronously, they may not be defined yet because
        // of the use of ng-if. If we wait one frame, then the element should be there if the ng-if
        // resolves to true.
        $$rAF(function() {
          // Handle the case where the md-icon element is initially hidden via ng-if from #9529.
          // We don't want to preserve the space for the icon in the case of ng-if, like we do for
          // ng-show.
          // Note that we can't use the same selectors from above because the elements are no longer
          // siblings for textareas at this point due to the insertion of the md-resize-wrapper.
          var iconNotRemoved = element[0].querySelector('md-icon') ||
            element[0].querySelector('.md-icon');
          if (hasLeftIcon && iconNotRemoved) {
            element.addClass('md-icon-left');
          }
          if (hasRightIcon && iconNotRemoved) {
            element.addClass('md-icon-right');
          }
        });
      }
    };
  }

  function ContainerCtrl($scope, $element, $attrs, $animate) {
    var self = this;

    self.isErrorGetter = $attrs.mdIsError && $parse($attrs.mdIsError);

    self.delegateClick = function() {
      self.input.focus();
    };
    self.element = $element;
    self.setFocused = function(isFocused) {
      $element.toggleClass('md-input-focused', !!isFocused);
    };
    self.setHasValue = function(hasValue) {
      $element.toggleClass('md-input-has-value', !!hasValue);
    };
    self.setHasPlaceholder = function(hasPlaceholder) {
      $element.toggleClass('md-input-has-placeholder', !!hasPlaceholder);
    };
    self.setInvalid = function(isInvalid) {
      if (isInvalid) {
        $animate.addClass($element, 'md-input-invalid');
      } else {
        $animate.removeClass($element, 'md-input-invalid');
      }
    };
    $scope.$watch(function() {
      return self.label && self.input;
    }, function(hasLabelAndInput) {
      if (hasLabelAndInput && !self.label.attr('for')) {
        self.label.attr('for', self.input.attr('id'));
      }
    });
  }
}

function labelDirective() {
  return {
    restrict: 'E',
    require: '^?mdInputContainer',
    link: function(scope, element, attr, containerCtrl) {
      if (!containerCtrl || attr.mdNoFloat || element.hasClass('md-container-ignore')) return;

      containerCtrl.label = element;
      scope.$on('$destroy', function() {
        containerCtrl.label = null;
      });
    }
  };
}

/**
 * @ngdoc directive
 * @name mdInput
 * @restrict E
 * @module material.components.input
 *
 * @description
 * You can use any `<input>` or `<textarea>` element as a child of an `<md-input-container>`. This
 * allows you to build complex forms for data entry.
 *
 * When the input is required and uses a floating label, then the label will automatically contain
 * an asterisk (`*`).<br/>
 * This behavior can be disabled by using the `md-no-asterisk` attribute.
 *
 * @param {number=} md-maxlength The maximum number of characters allowed in this input. If this is
 *   specified, a character counter will be shown underneath the input.<br/><br/>
 *   The purpose of **`md-maxlength`** is exactly to show the max length counter text. If you don't
 *   want the counter text and only need "plain" validation, you can use the "simple" `ng-maxlength`
 *   or maxlength attributes.<br/><br/>
 * @param {boolean=} ng-trim If set to false, the input text will be not trimmed automatically.
 *     Defaults to true.
 * @param {string=} aria-label Aria-label is required when no label is present.  A warning message
 *   will be logged in the console if not present.
 * @param {string=} placeholder An alternative approach to using aria-label when the label is not
 *   PRESENT. The placeholder text is copied to the aria-label attribute.
 * @param {boolean=} md-no-autogrow When present, textareas will not grow automatically.
 * @param {boolean=} md-no-asterisk When present, an asterisk will not be appended to the inputs
 *   floating label.
 * @param {boolean=} md-no-resize Disables the textarea resize handle.
 * @param {number=} max-rows The maximum amount of rows for a textarea.
 * @param {boolean=} md-detect-hidden When present, textareas will be sized properly when they are
 *   revealed after being hidden. This is off by default for performance reasons because it
 *   guarantees a reflow every digest cycle.
 *
 * @usage
 * <hljs lang="html">
 * <md-input-container>
 *   <label>Color</label>
 *   <input type="text" ng-model="color" required md-maxlength="10">
 * </md-input-container>
 * </hljs>
 *
 * <h3>With Errors</h3>
 *
 * `md-input-container` also supports errors using the standard `ng-messages` directives and
 * animates the messages when they become visible using from the `ngEnter`/`ngLeave` events or
 * the `ngShow`/`ngHide` events.
 *
 * By default, the messages will be hidden until the input is in an error state. This is based off
 * of the `md-is-error` expression of the `md-input-container`. This gives the user a chance to
 * fill out the form before the errors become visible.
 *
 * <hljs lang="html">
 * <form name="colorForm">
 *   <md-input-container>
 *     <label>Favorite Color</label>
 *     <input name="favoriteColor" ng-model="favoriteColor" required>
 *     <div ng-messages="colorForm.favoriteColor.$error">
 *       <div ng-message="required">This is required!</div>
 *     </div>
 *   </md-input-container>
 * </form>
 * </hljs>
 *
 * We automatically disable this auto-hiding functionality if you provide any of the following
 * visibility directives on the `ng-messages` container:
 *
 *  - `ng-if`
 *  - `ng-show`/`ng-hide`
 *  - `ng-switch-when`/`ng-switch-default`
 *
 * You can also disable this functionality manually by adding the `md-auto-hide="false"` expression
 * to the `ng-messages` container. This may be helpful if you always want to see the error messages
 * or if you are building your own visibility directive.
 *
 * _<b>Note:</b> The `md-auto-hide` attribute is a static string that is  only checked upon
 * initialization of the `ng-messages` directive to see if it equals the string `false`._
 *
 * <hljs lang="html">
 * <form name="userForm">
 *   <md-input-container>
 *     <label>Last Name</label>
 *     <input name="lastName" ng-model="lastName" required md-maxlength="10" minlength="4">
 *     <div ng-messages="userForm.lastName.$error" ng-show="userForm.lastName.$dirty">
 *       <div ng-message="required">This is required!</div>
 *       <div ng-message="md-maxlength">That's too long!</div>
 *       <div ng-message="minlength">That's too short!</div>
 *     </div>
 *   </md-input-container>
 *   <md-input-container>
 *     <label>Biography</label>
 *     <textarea name="bio" ng-model="biography" required md-maxlength="150"></textarea>
 *     <div ng-messages="userForm.bio.$error" ng-show="userForm.bio.$dirty">
 *       <div ng-message="required">This is required!</div>
 *       <div ng-message="md-maxlength">That's too long!</div>
 *     </div>
 *   </md-input-container>
 *   <md-input-container>
 *     <input aria-label='title' ng-model='title'>
 *   </md-input-container>
 *   <md-input-container>
 *     <input placeholder='title' ng-model='title'>
 *   </md-input-container>
 * </form>
 * </hljs>
 *
 * <h3>Notes</h3>
 *
 * - Requires [ngMessages](https://docs.angularjs.org/api/ngMessages).
 * - Behaves like the [AngularJS input directive](https://docs.angularjs.org/api/ng/directive/input).
 *
 * The `md-input` and `md-input-container` directives use very specific positioning to achieve the
 * error animation effects. Therefore, it is *not* advised to use the Layout system inside of the
 * `<md-input-container>` tags. Instead, use relative or absolute positioning.
 *
 *
 * <h3>Textarea directive</h3>
 * The `textarea` element within a `md-input-container` has the following specific behavior:
 * - By default the `textarea` grows as the user types. This can be disabled via the `md-no-autogrow`
 * attribute.
 * - If a `textarea` has the `rows` attribute, it will treat the `rows` as the minimum height and will
 * continue growing as the user types. For example a textarea with `rows="3"` will be 3 lines of text
 * high initially. If no rows are specified, the directive defaults to 1.
 * - The textarea's height gets set on initialization, as well as while the user is typing. In certain situations
 * (e.g. while animating) the directive might have been initialized, before the element got it's final height. In
 * those cases, you can trigger a resize manually by broadcasting a `md-resize-textarea` event on the scope.
 * - If you want a `textarea` to stop growing at a certain point, you can specify the `max-rows` attribute.
 * - The textarea's bottom border acts as a handle which users can drag, in order to resize the element vertically.
 * Once the user has resized a `textarea`, the autogrowing functionality becomes disabled. If you don't want a
 * `textarea` to be resizeable by the user, you can add the `md-no-resize` attribute.
 */

function inputTextareaDirective($mdUtil, $window, $mdAria, $timeout, $mdGesture) {
  return {
    restrict: 'E',
    require: ['^?mdInputContainer', '?ngModel', '?^form'],
    link: postLink
  };

  function postLink(scope, element, attr, ctrls) {

    var containerCtrl = ctrls[0];
    var hasNgModel = !!ctrls[1];
    var ngModelCtrl = ctrls[1] || $mdUtil.fakeNgModel();
    var parentForm = ctrls[2];
    var isReadonly = angular.isDefined(attr.readonly);
    var mdNoAsterisk = $mdUtil.parseAttributeBoolean(attr.mdNoAsterisk);
    var tagName = element[0].tagName.toLowerCase();


    if (!containerCtrl) return;
    if (attr.type === 'hidden') {
      element.attr('aria-hidden', 'true');
      return;
    } else if (containerCtrl.input) {
      if (containerCtrl.input[0].contains(element[0])) {
        return;
      } else {
        throw new Error("<md-input-container> can only have *one* <input>, <textarea> or <md-select> child element!");
      }
    }
    containerCtrl.input = element;

    setupAttributeWatchers();

    // Add an error spacer div after our input to provide space for the char counter and any ng-messages
    var errorsSpacer = angular.element('<div class="md-errors-spacer">');
    element.after(errorsSpacer);

    var placeholderText = angular.isString(attr.placeholder) ? attr.placeholder.trim() : '';
    if (!containerCtrl.label && !placeholderText.length) {
      $mdAria.expect(element, 'aria-label');
    }

    element.addClass('md-input');
    if (!element.attr('id')) {
      element.attr('id', 'input_' + $mdUtil.nextUid());
    }

    // This works around a Webkit issue where number inputs, placed in a flexbox, that have
    // a `min` and `max` will collapse to about 1/3 of their proper width. Please check #7349
    // for more info. Also note that we don't override the `step` if the user has specified it,
    // in order to prevent some unexpected behaviour.
    if (tagName === 'input' && attr.type === 'number' && attr.min && attr.max && !attr.step) {
      element.attr('step', 'any');
    } else if (tagName === 'textarea') {
      setupTextarea();
    }

    // If the input doesn't have an ngModel, it may have a static value. For that case,
    // we have to do one initial check to determine if the container should be in the
    // "has a value" state.
    if (!hasNgModel) {
      inputCheckValue();
    }

    var isErrorGetter = containerCtrl.isErrorGetter || function() {
      return ngModelCtrl.$invalid && (ngModelCtrl.$touched || (parentForm && parentForm.$submitted));
    };

    scope.$watch(isErrorGetter, containerCtrl.setInvalid);

    // When the developer uses the ngValue directive for the input, we have to observe the attribute, because
    // AngularJS's ngValue directive is just setting the `value` attribute.
    if (attr.ngValue) {
      attr.$observe('value', inputCheckValue);
    }

    ngModelCtrl.$parsers.push(ngModelPipelineCheckValue);
    ngModelCtrl.$formatters.push(ngModelPipelineCheckValue);

    element.on('input', inputCheckValue);

    if (!isReadonly) {
      element
        .on('focus', function(ev) {
          $mdUtil.nextTick(function() {
            containerCtrl.setFocused(true);
          });
        })
        .on('blur', function(ev) {
          $mdUtil.nextTick(function() {
            containerCtrl.setFocused(false);
            inputCheckValue();
          });
        });
    }

    scope.$on('$destroy', function() {
      containerCtrl.setFocused(false);
      containerCtrl.setHasValue(false);
      containerCtrl.input = null;
    });

    /** Gets run through ngModel's pipeline and set the `has-value` class on the container. */
    function ngModelPipelineCheckValue(arg) {
      containerCtrl.setHasValue(!ngModelCtrl.$isEmpty(arg));
      return arg;
    }

    function setupAttributeWatchers() {
      if (containerCtrl.label) {
        attr.$observe('required', function (value) {
          // We don't need to parse the required value, it's always a boolean because of angular's
          // required directive.
          containerCtrl.label.toggleClass('md-required', value && !mdNoAsterisk);
        });
      }
    }

    function inputCheckValue() {
      // An input's value counts if its length > 0,
      // or if the input's validity state says it has bad input (eg string in a number input)
      containerCtrl.setHasValue(element.val().length > 0 || (element[0].validity || {}).badInput);
    }

    function setupTextarea() {
      var isAutogrowing = !attr.hasOwnProperty('mdNoAutogrow');

      attachResizeHandle();

      if (!isAutogrowing) return;

      // Can't check if height was or not explicity set,
      // so rows attribute will take precedence if present
      var minRows = attr.hasOwnProperty('rows') ? parseInt(attr.rows) : NaN;
      var maxRows = attr.hasOwnProperty('maxRows') ? parseInt(attr.maxRows) : NaN;
      var scopeResizeListener = scope.$on('md-resize-textarea', growTextarea);
      var lineHeight = null;
      var node = element[0];

      // This timeout is necessary, because the browser needs a little bit
      // of time to calculate the `clientHeight` and `scrollHeight`.
      $timeout(function() {
        $mdUtil.nextTick(growTextarea);
      }, 10, false);

      // We could leverage ngModel's $parsers here, however it
      // isn't reliable, because AngularJS trims the input by default,
      // which means that growTextarea won't fire when newlines and
      // spaces are added.
      element.on('input', growTextarea);

      // We should still use the $formatters, because they fire when
      // the value was changed from outside the textarea.
      if (hasNgModel) {
        ngModelCtrl.$formatters.push(formattersListener);
      }

      if (!minRows) {
        element.attr('rows', 1);
      }

      angular.element($window).on('resize', growTextarea);
      scope.$on('$destroy', disableAutogrow);

      function growTextarea() {
        // temporarily disables element's flex so its height 'runs free'
        element
          .attr('rows', 1)
          .css('height', 'auto')
          .addClass('md-no-flex');

        var height = getHeight();

        if (!lineHeight) {
          // offsetHeight includes padding which can throw off our value
          var originalPadding = element[0].style.padding || '';
          lineHeight = element.css('padding', 0).prop('offsetHeight');
          element[0].style.padding = originalPadding;
        }

        if (minRows && lineHeight) {
          height = Math.max(height, lineHeight * minRows);
        }

        if (maxRows && lineHeight) {
          var maxHeight = lineHeight * maxRows;

          if (maxHeight < height) {
            element.attr('md-no-autogrow', '');
            height = maxHeight;
          } else {
            element.removeAttr('md-no-autogrow');
          }
        }

        if (lineHeight) {
          element.attr('rows', Math.round(height / lineHeight));
        }

        element
          .css('height', height + 'px')
          .removeClass('md-no-flex');
      }

      function getHeight() {
        var offsetHeight = node.offsetHeight;
        var line = node.scrollHeight - offsetHeight;
        return offsetHeight + Math.max(line, 0);
      }

      function formattersListener(value) {
        $mdUtil.nextTick(growTextarea);
        return value;
      }

      function disableAutogrow() {
        if (!isAutogrowing) return;

        isAutogrowing = false;
        angular.element($window).off('resize', growTextarea);
        scopeResizeListener && scopeResizeListener();
        element
          .attr('md-no-autogrow', '')
          .off('input', growTextarea);

        if (hasNgModel) {
          var listenerIndex = ngModelCtrl.$formatters.indexOf(formattersListener);

          if (listenerIndex > -1) {
            ngModelCtrl.$formatters.splice(listenerIndex, 1);
          }
        }
      }

      function attachResizeHandle() {
        if (attr.hasOwnProperty('mdNoResize')) return;

        var handle = angular.element('<div class="md-resize-handle"></div>');
        var isDragging = false;
        var dragStart = null;
        var startHeight = 0;
        var container = containerCtrl.element;
        var dragGestureHandler = $mdGesture.register(handle, 'drag', { horizontal: false });


        element.wrap('<div class="md-resize-wrapper">').after(handle);
        handle.on('mousedown', onMouseDown);

        container
          .on('$md.dragstart', onDragStart)
          .on('$md.drag', onDrag)
          .on('$md.dragend', onDragEnd);

        scope.$on('$destroy', function() {
          handle
            .off('mousedown', onMouseDown)
            .remove();

          container
            .off('$md.dragstart', onDragStart)
            .off('$md.drag', onDrag)
            .off('$md.dragend', onDragEnd);

          dragGestureHandler();
          handle = null;
          container = null;
          dragGestureHandler = null;
        });

        function onMouseDown(ev) {
          ev.preventDefault();
          isDragging = true;
          dragStart = ev.clientY;
          startHeight = parseFloat(element.css('height')) || element.prop('offsetHeight');
        }

        function onDragStart(ev) {
          if (!isDragging) return;
          ev.preventDefault();
          disableAutogrow();
          container.addClass('md-input-resized');
        }

        function onDrag(ev) {
          if (!isDragging) return;

          element.css('height', (startHeight + ev.pointer.distanceY) + 'px');
        }

        function onDragEnd(ev) {
          if (!isDragging) return;
          isDragging = false;
          container.removeClass('md-input-resized');
        }
      }

      // Attach a watcher to detect when the textarea gets shown.
      if (attr.hasOwnProperty('mdDetectHidden')) {

        var handleHiddenChange = function() {
          var wasHidden = false;

          return function() {
            var isHidden = node.offsetHeight === 0;

            if (isHidden === false && wasHidden === true) {
              growTextarea();
            }

            wasHidden = isHidden;
          };
        }();

        // Check every digest cycle whether the visibility of the textarea has changed.
        // Queue up to run after the digest cycle is complete.
        scope.$watch(function() {
          $mdUtil.nextTick(handleHiddenChange, false);
          return true;
        });
      }
    }
  }
}

function mdMaxlengthDirective($animate, $mdUtil) {
  return {
    restrict: 'A',
    require: ['ngModel', '^mdInputContainer'],
    link: postLink
  };

  function postLink(scope, element, attr, ctrls) {
    var maxlength = parseInt(attr.mdMaxlength);
    if (isNaN(maxlength)) maxlength = -1;
    var ngModelCtrl = ctrls[0];
    var containerCtrl = ctrls[1];
    var charCountEl, errorsSpacer;
    var ngTrim = angular.isDefined(attr.ngTrim) ? $mdUtil.parseAttributeBoolean(attr.ngTrim) : true;
    var isPasswordInput = attr.type === 'password';

    scope.$watch(attr.mdMaxlength, function(value) {
      maxlength = value;
    });

    ngModelCtrl.$validators['md-maxlength'] = function(modelValue, viewValue) {
      if (!angular.isNumber(maxlength) || maxlength < 0) {
        return true;
      }

      // We always update the char count, when the modelValue has changed.
      // Using the $validators for triggering the update works very well.
      renderCharCount();

      var elementVal = element.val() || viewValue;
      if (elementVal === undefined || elementVal === null) {
        elementVal = '';
      }
      elementVal = ngTrim && !isPasswordInput && angular.isString(elementVal) ? elementVal.trim() : elementVal;
      // Force the value into a string since it may be a number,
      // which does not have a length property.
      return String(elementVal).length <= maxlength;
    };

    /**
     * Override the default NgModelController $isEmpty check to take ng-trim, password inputs,
     * etc. into account.
     * @param value {*} the input's value
     * @returns {boolean} true if the input's value should be considered empty, false otherwise
     */
    ngModelCtrl.$isEmpty = function(value) {
      return calculateInputValueLength(value) === 0;
    };

    // Wait until the next tick to ensure that the input has setup the errors spacer where we will
    // append our counter
    $mdUtil.nextTick(function() {
      errorsSpacer = angular.element(containerCtrl.element[0].querySelector('.md-errors-spacer'));
      charCountEl = angular.element('<div class="md-char-counter">');

      // Append our character counter inside the errors spacer
      errorsSpacer.append(charCountEl);

      attr.$observe('ngTrim', function (value) {
        ngTrim = angular.isDefined(value) ? $mdUtil.parseAttributeBoolean(value) : true;
      });

      scope.$watch(attr.mdMaxlength, function(value) {
        if (angular.isNumber(value) && value > 0) {
          if (!charCountEl.parent().length) {
            $animate.enter(charCountEl, errorsSpacer);
          }
          renderCharCount();
        } else {
          $animate.leave(charCountEl);
        }
      });
    });

    /**
     * Calculate the input value's length after coercing it to a string
     * and trimming it if appropriate.
     * @param value {*} the input's value
     * @returns {number} calculated length of the input's value
     */
    function calculateInputValueLength(value) {
      value = ngTrim && !isPasswordInput && angular.isString(value) ? value.trim() : value;
      if (value === undefined || value === null) {
        value = '';
      }
      return String(value).length;
    }

    function renderCharCount() {
      // If we have not been initialized or appended to the body yet; do not render.
      if (!charCountEl || !charCountEl.parent()) {
        return;
      }
      // Force the value into a string since it may be a number,
      // which does not have a length property.
      charCountEl.text(calculateInputValueLength(element.val()) + ' / ' + maxlength);
    }
  }
}

function placeholderDirective($compile) {
  return {
    restrict: 'A',
    require: '^^?mdInputContainer',
    priority: 200,
    link: {
      // Note that we need to do this in the pre-link, as opposed to the post link, if we want to
      // support data bindings in the placeholder. This is necessary, because we have a case where
      // we transfer the placeholder value to the `<label>` and we remove it from the original `<input>`.
      // If we did this in the post-link, AngularJS would have set up the observers already and would be
      // re-adding the attribute, even though we removed it from the element.
      pre: preLink
    }
  };

  function preLink(scope, element, attr, inputContainer) {
    // If there is no input container, just return
    if (!inputContainer) return;

    var label = inputContainer.element.find('label');
    var noFloat = inputContainer.element.attr('md-no-float');

    // If we have a label, or they specify the md-no-float attribute, just return
    if ((label && label.length) || noFloat === '' || scope.$eval(noFloat)) {
      // Add a placeholder class so we can target it in the CSS
      inputContainer.setHasPlaceholder(true);
      return;
    }

    // md-select handles placeholders on it's own
    if (element[0].nodeName !== 'MD-SELECT') {
      // Move the placeholder expression to the label
      var newLabel = angular.element(
        '<label ng-click="delegateClick()" tabindex="-1" aria-hidden="true">' + attr.placeholder +
        '</label>');

      // Note that we unset it via `attr`, in order to get AngularJS
      // to remove any observers that it might have set up. Otherwise
      // the attribute will be added on the next digest.
      attr.$set('placeholder', null);

      // We need to compile the label manually in case it has any bindings.
      // A gotcha here is that we first add the element to the DOM and we compile
      // it later. This is necessary, because if we compile the element beforehand,
      // it won't be able to find the `mdInputContainer` controller.
      inputContainer.element
        .addClass('md-icon-float')
        .prepend(newLabel);

      $compile(newLabel)(scope);
    }
  }
}

/**
 * @ngdoc directive
 * @name mdSelectOnFocus
 * @module material.components.input
 *
 * @restrict A
 *
 * @description
 * The `md-select-on-focus` directive allows you to automatically select the element's input text on focus.
 *
 * <h3>Notes</h3>
 * - The use of `md-select-on-focus` is restricted to `<input>` and `<textarea>` elements.
 *
 * @usage
 * <h3>Using with an Input</h3>
 * <hljs lang="html">
 *
 * <md-input-container>
 *   <label>Auto Select</label>
 *   <input type="text" md-select-on-focus>
 * </md-input-container>
 * </hljs>
 *
 * <h3>Using with a Textarea</h3>
 * <hljs lang="html">
 *
 * <md-input-container>
 *   <label>Auto Select</label>
 *   <textarea md-select-on-focus>This text will be selected on focus.</textarea>
 * </md-input-container>
 *
 * </hljs>
 */
function mdSelectOnFocusDirective($document, $timeout) {

  return {
    restrict: 'A',
    link: postLink
  };

  function postLink(scope, element, attr) {
    if (element[0].nodeName !== 'INPUT' && element[0].nodeName !== "TEXTAREA") return;

    var preventMouseUp = false;

    element
      .on('focus', onFocus)
      .on('mouseup', onMouseUp);

    scope.$on('$destroy', function() {
      element
        .off('focus', onFocus)
        .off('mouseup', onMouseUp);
    });

    function onFocus() {
      preventMouseUp = true;

      $timeout(function() {

        // Use HTMLInputElement#select to fix firefox select issues.
        // The debounce is here for Edge's sake, otherwise the selection doesn't work.
        // Since focus may already have been lost on the input (and because `select()`
        // will re-focus), make sure the element is still active before applying.
        if ($document[0].activeElement === element[0]) {
          element[0].select();
        }

        // This should be reset from inside the `focus`, because the event might
        // have originated from something different than a click, e.g. a keyboard event.
        preventMouseUp = false;
      }, 1, false);
    }

    // Prevents the default action of the first `mouseup` after a focus.
    // This is necessary, because browsers fire a `mouseup` right after the element
    // has been focused. In some browsers (Firefox in particular) this can clear the
    // selection. There are examples of the problem in issue #7487.
    function onMouseUp(event) {
      if (preventMouseUp) {
        event.preventDefault();
      }
    }
  }
}

var visibilityDirectives = ['ngIf', 'ngShow', 'ngHide', 'ngSwitchWhen', 'ngSwitchDefault'];
function ngMessagesDirective() {
  return {
    restrict: 'EA',
    link: postLink,

    // This is optional because we don't want target *all* ngMessage instances, just those inside of
    // mdInputContainer.
    require: '^^?mdInputContainer'
  };

  function postLink(scope, element, attrs, inputContainer) {
    // If we are not a child of an input container, don't do anything
    if (!inputContainer) return;

    // Add our animation class
    element.toggleClass('md-input-messages-animation', true);

    // Add our md-auto-hide class to automatically hide/show messages when container is invalid
    element.toggleClass('md-auto-hide', true);

    // If we see some known visibility directives, remove the md-auto-hide class
    if (attrs.mdAutoHide == 'false' || hasVisibiltyDirective(attrs)) {
      element.toggleClass('md-auto-hide', false);
    }
  }

  function hasVisibiltyDirective(attrs) {
    return visibilityDirectives.some(function(attr) {
      return attrs[attr];
    });
  }
}

function ngMessageDirective($mdUtil) {
  return {
    restrict: 'EA',
    compile: compile,
    priority: 100
  };

  function compile(tElement) {
    if (!isInsideInputContainer(tElement)) {

      // When the current element is inside of a document fragment, then we need to check for an input-container
      // in the postLink, because the element will be later added to the DOM and is currently just in a temporary
      // fragment, which causes the input-container check to fail.
      if (isInsideFragment()) {
        return function (scope, element) {
          if (isInsideInputContainer(element)) {
            // Inside of the postLink function, a ngMessage directive will be a comment element, because it's
            // currently hidden. To access the shown element, we need to use the element from the compile function.
            initMessageElement(tElement);
          }
        };
      }
    } else {
      initMessageElement(tElement);
    }

    function isInsideFragment() {
      var nextNode = tElement[0];
      while (nextNode = nextNode.parentNode) {
        if (nextNode.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
          return true;
        }
      }
      return false;
    }

    function isInsideInputContainer(element) {
      return !!$mdUtil.getClosest(element, "md-input-container");
    }

    function initMessageElement(element) {
      // Add our animation class
      element.toggleClass('md-input-message-animation', true);
    }
  }
}

var $$AnimateRunner, $animateCss, $mdUtil;

function mdInputInvalidMessagesAnimation($$AnimateRunner, $animateCss, $mdUtil) {
  saveSharedServices($$AnimateRunner, $animateCss, $mdUtil);

  return {
    addClass: function(element, className, done) {
      showInputMessages(element, done);
    }

    // NOTE: We do not need the removeClass method, because the message ng-leave animation will fire
  };
}

function ngMessagesAnimation($$AnimateRunner, $animateCss, $mdUtil) {
  saveSharedServices($$AnimateRunner, $animateCss, $mdUtil);

  return {
    enter: function(element, done) {
      showInputMessages(element, done);
    },

    leave: function(element, done) {
      hideInputMessages(element, done);
    },

    addClass: function(element, className, done) {
      if (className == "ng-hide") {
        hideInputMessages(element, done);
      } else {
        done();
      }
    },

    removeClass: function(element, className, done) {
      if (className == "ng-hide") {
        showInputMessages(element, done);
      } else {
        done();
      }
    }
  };
}

function ngMessageAnimation($$AnimateRunner, $animateCss, $mdUtil, $log) {
  saveSharedServices($$AnimateRunner, $animateCss, $mdUtil, $log);

  return {
    enter: function(element, done) {
      var animator = showMessage(element);

      animator.start().done(done);
    },

    leave: function(element, done) {
      var animator = hideMessage(element);

      animator.start().done(done);
    }
  };
}

function showInputMessages(element, done) {
  var animators = [], animator;
  var messages = getMessagesElement(element);
  var children = messages.children();

  if (messages.length == 0 || children.length == 0) {
    done();
    return;
  }

  angular.forEach(children, function(child) {
    animator = showMessage(angular.element(child));

    animators.push(animator.start());
  });

  $$AnimateRunner.all(animators, done);
}

function hideInputMessages(element, done) {
  var animators = [], animator;
  var messages = getMessagesElement(element);
  var children = messages.children();

  if (messages.length == 0 || children.length == 0) {
    done();
    return;
  }

  angular.forEach(children, function(child) {
    animator = hideMessage(angular.element(child));

    animators.push(animator.start());
  });

  $$AnimateRunner.all(animators, done);
}

function showMessage(element) {
  var height = parseInt(window.getComputedStyle(element[0]).height);
  var topMargin = parseInt(window.getComputedStyle(element[0]).marginTop);

  var messages = getMessagesElement(element);
  var container = getInputElement(element);

  // Check to see if the message is already visible so we can skip
  var alreadyVisible = (topMargin > -height);

  // If we have the md-auto-hide class, the md-input-invalid animation will fire, so we can skip
  if (alreadyVisible || (messages.hasClass('md-auto-hide') && !container.hasClass('md-input-invalid'))) {
    return $animateCss(element, {});
  }

  return $animateCss(element, {
    event: 'enter',
    structural: true,
    from: {"opacity": 0, "margin-top": -height + "px"},
    to: {"opacity": 1, "margin-top": "0"},
    duration: 0.3
  });
}

function hideMessage(element) {
  var height = element[0].offsetHeight;
  var styles = window.getComputedStyle(element[0]);

  // If we are already hidden, just return an empty animation
  if (parseInt(styles.opacity) === 0) {
    return $animateCss(element, {});
  }

  // Otherwise, animate
  return $animateCss(element, {
    event: 'leave',
    structural: true,
    from: {"opacity": 1, "margin-top": 0},
    to: {"opacity": 0, "margin-top": -height + "px"},
    duration: 0.3
  });
}

function getInputElement(element) {
  var inputContainer = element.controller('mdInputContainer');

  return inputContainer.element;
}

function getMessagesElement(element) {
  // If we ARE the messages element, just return ourself
  if (element.hasClass('md-input-messages-animation')) {
    return element;
  }

  // If we are a ng-message element, we need to traverse up the DOM tree
  if (element.hasClass('md-input-message-animation')) {
    return angular.element($mdUtil.getClosest(element, function(node) {
      return node.classList.contains('md-input-messages-animation');
    }));
  }

  // Otherwise, we can traverse down
  return angular.element(element[0].querySelector('.md-input-messages-animation'));
}

function saveSharedServices(_$$AnimateRunner_, _$animateCss_, _$mdUtil_) {
  $$AnimateRunner = _$$AnimateRunner_;
  $animateCss = _$animateCss_;
  $mdUtil = _$mdUtil_;
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.list
 * @description
 * List module
 */
MdListController.$inject = ["$scope", "$element", "$mdListInkRipple"];
mdListDirective.$inject = ["$mdTheming"];
mdListItemDirective.$inject = ["$mdAria", "$mdConstant", "$mdUtil", "$timeout"];
angular.module('material.components.list', [
  'material.core'
])
  .controller('MdListController', MdListController)
  .directive('mdList', mdListDirective)
  .directive('mdListItem', mdListItemDirective);

/**
 * @ngdoc directive
 * @name mdList
 * @module material.components.list
 *
 * @restrict E
 *
 * @description
 * The `<md-list>` directive is a list container for 1..n `<md-list-item>` tags.
 *
 * @usage
 * <hljs lang="html">
 * <md-list>
 *   <md-list-item class="md-2-line" ng-repeat="item in todos">
 *     <md-checkbox ng-model="item.done"></md-checkbox>
 *     <div class="md-list-item-text">
 *       <h3>{{item.title}}</h3>
 *       <p>{{item.description}}</p>
 *     </div>
 *   </md-list-item>
 * </md-list>
 * </hljs>
 */

function mdListDirective($mdTheming) {
  return {
    restrict: 'E',
    compile: function(tEl) {
      tEl[0].setAttribute('role', 'list');
      return $mdTheming;
    }
  };
}
/**
 * @ngdoc directive
 * @name mdListItem
 * @module material.components.list
 *
 * @restrict E
 *
 * @description
 * A `md-list-item` element can be used to represent some information in a row.<br/>
 *
 * @usage
 * ### Single Row Item
 * <hljs lang="html">
 *   <md-list-item>
 *     <span>Single Row Item</span>
 *   </md-list-item>
 * </hljs>
 *
 * ### Multiple Lines
 * By using the following markup, you will be able to have two lines inside of one `md-list-item`.
 *
 * <hljs lang="html">
 *   <md-list-item class="md-2-line">
 *     <div class="md-list-item-text" layout="column">
 *       <p>First Line</p>
 *       <p>Second Line</p>
 *     </div>
 *   </md-list-item>
 * </hljs>
 *
 * It is also possible to have three lines inside of one list item.
 *
 * <hljs lang="html">
 *   <md-list-item class="md-3-line">
 *     <div class="md-list-item-text" layout="column">
 *       <p>First Line</p>
 *       <p>Second Line</p>
 *       <p>Third Line</p>
 *     </div>
 *   </md-list-item>
 * </hljs>
 *
 * ### Secondary Items
 * Secondary items are elements which will be aligned at the end of the `md-list-item`.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <span>Single Row Item</span>
 *     <md-button class="md-secondary">
 *       Secondary Button
 *     </md-button>
 *   </md-list-item>
 * </hljs>
 *
 * It also possible to have multiple secondary items inside of one `md-list-item`.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <span>Single Row Item</span>
 *     <md-button class="md-secondary">First Button</md-button>
 *     <md-button class="md-secondary">Second Button</md-button>
 *   </md-list-item>
 * </hljs>
 *
 * ### Proxy Item
 * Proxies are elements, which will execute their specific action on click<br/>
 * Currently supported proxy items are
 * - `md-checkbox` (Toggle)
 * - `md-switch` (Toggle)
 * - `md-menu` (Open)
 *
 * This means, when using a supported proxy item inside of `md-list-item`, the list item will
 * automatically become clickable and executes the associated action of the proxy element on click.
 *
 * It is possible to disable this behavior by applying the `md-no-proxy` class to the list item.
 *
 * <hljs lang="html">
 *   <md-list-item class="md-no-proxy">
 *     <span>No Proxy List</span>
 *     <md-checkbox class="md-secondary"></md-checkbox>
 *   </md-list-item>
 * </hljs>
 *
 * Here are a few examples of proxy elements inside of a list item.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <span>First Line</span>
 *     <md-checkbox class="md-secondary"></md-checkbox>
 *   </md-list-item>
 * </hljs>
 *
 * The `md-checkbox` element will be automatically detected as a proxy element and will toggle on
 * click.
 *
 * If not provided, an `aria-label` will be applied using the text of the list item.
 * In this case, the following will be applied to the `md-checkbox`:
 * `aria-label="Toggle First Line"`.
 * When localizing your application, you should supply a localized `aria-label`.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <span>First Line</span>
 *     <md-switch class="md-secondary"></md-switch>
 *   </md-list-item>
 * </hljs>
 *
 * The recognized `md-switch` will toggle its state, when the user clicks on the `md-list-item`.
 *
 * It is also possible to have a `md-menu` inside of a `md-list-item`.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <p>Click anywhere to fire the secondary action</p>
 *     <md-menu class="md-secondary">
 *       <md-button class="md-icon-button">
 *         <md-icon md-svg-icon="communication:message"></md-icon>
 *       </md-button>
 *       <md-menu-content width="4">
 *         <md-menu-item>
 *           <md-button>
 *             Redial
 *           </md-button>
 *         </md-menu-item>
 *         <md-menu-item>
 *           <md-button>
 *             Check voicemail
 *           </md-button>
 *         </md-menu-item>
 *         <md-menu-divider></md-menu-divider>
 *         <md-menu-item>
 *           <md-button>
 *             Notifications
 *           </md-button>
 *         </md-menu-item>
 *       </md-menu-content>
 *     </md-menu>
 *   </md-list-item>
 * </hljs>
 *
 * The menu will automatically open, when the users clicks on the `md-list-item`.<br/>
 *
 * If the developer didn't specify any position mode on the menu, the `md-list-item` will
 * automatically detect the position mode and apply it to the `md-menu`.
 *
 * ### Avatars
 * Sometimes you may want to have avatars inside of the `md-list-item `.<br/>
 * You are able to create an optimized icon for the list item, by applying the `.md-avatar` class on
 * the `<img>` element.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <img src="my-avatar.png" class="md-avatar">
 *     <span>Alan Turing</span>
 * </hljs>
 *
 * When using `<md-icon>` for an avatar, you have to use the `.md-avatar-icon` class.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <md-icon class="md-avatar-icon" md-svg-icon="social:person"></md-icon>
 *     <span>Timothy Kopra</span>
 *   </md-list-item>
 * </hljs>
 *
 * In cases where you have a `md-list-item`, which doesn't have an avatar,
 * but you want to align it with the other avatar items, you need to use the `.md-offset` class.
 *
 * <hljs lang="html">
 *   <md-list-item class="md-offset">
 *     <span>Jon Doe</span>
 *   </md-list-item>
 * </hljs>
 *
 * ### DOM modification
 * The `md-list-item` component automatically detects if the list item should be clickable.
 *
 * ---
 * If the `md-list-item` is clickable, we wrap all content inside of a `<div>` and create
 * an overlaying button, which will will execute the given actions (like `ng-href`, `ng-click`).
 *
 * We create an overlaying button, instead of wrapping all content inside of the button,
 * because otherwise some elements may not be clickable inside of the button.
 *
 * ---
 * When using a secondary item inside of your list item, the `md-list-item` component will
 * automatically create a secondary container at the end of the `md-list-item`, which contains all
 * secondary items.
 *
 * The secondary item container is not static, because that would cause issues with the overflow
 * of the list item.
 */
function mdListItemDirective($mdAria, $mdConstant, $mdUtil, $timeout) {
  var proxiedTypes = ['md-checkbox', 'md-switch', 'md-menu'];
  return {
    restrict: 'E',
    controller: 'MdListController',

    compile: function(tElement, tAttrs) {

      // Check for proxy controls (no ng-click on parent, and a control inside)
      var secondaryItems = tElement[0].querySelectorAll('.md-secondary');
      var hasProxiedElement;
      var proxyElement;
      var itemContainer = tElement;

      tElement[0].setAttribute('role', 'listitem');

      if (tAttrs.ngClick || tAttrs.ngDblclick ||  tAttrs.ngHref || tAttrs.href || tAttrs.uiSref || tAttrs.ngAttrUiSref) {
        wrapIn('button');
      } else if (!tElement.hasClass('md-no-proxy')) {

        for (var i = 0, type; i < proxiedTypes.length; ++i) {
          proxyElement = tElement[0].querySelector(proxiedTypes[i]);
          if (proxyElement !== null) {
            hasProxiedElement = true;
            break;
          }
        }

        if (hasProxiedElement) {
          wrapIn('div');
        } else {
          tElement.addClass('md-no-proxy');
        }
      }

      wrapSecondaryItems();
      setupToggleAria();

      if (hasProxiedElement && proxyElement.nodeName === "MD-MENU") {
        setupProxiedMenu();
      }

      function setupToggleAria() {
        var toggleTypes = ['md-switch', 'md-checkbox'];
        var toggle;

        for (var i = 0, toggleType; i < toggleTypes.length; ++i) {
          toggle = tElement.find(toggleTypes[i])[0];
          if (toggle) {
            if (!toggle.hasAttribute('aria-label')) {
              var labelElement = tElement.find('p')[0];
              if (!labelElement) {
                labelElement = tElement.find('span')[0];
              }
              if (!labelElement) return;
              toggle.setAttribute('aria-label', 'Toggle ' + labelElement.textContent);
            }
          }
        }
      }

      function setupProxiedMenu() {
        var menuEl = angular.element(proxyElement);

        var isEndAligned = menuEl.parent().hasClass('md-secondary-container') ||
                           proxyElement.parentNode.firstElementChild !== proxyElement;

        var xAxisPosition = 'left';

        if (isEndAligned) {
          // When the proxy item is aligned at the end of the list, we have to set the origin to the end.
          xAxisPosition = 'right';
        }

        // Set the position mode / origin of the proxied menu.
        if (!menuEl.attr('md-position-mode')) {
          menuEl.attr('md-position-mode', xAxisPosition + ' target');
        }

        // Apply menu open binding to menu button
        var menuOpenButton = menuEl.children().eq(0);
        if (!hasClickEvent(menuOpenButton[0])) {
          menuOpenButton.attr('ng-click', '$mdMenu.open($event)');
        }

        if (!menuOpenButton.attr('aria-label')) {
          menuOpenButton.attr('aria-label', 'Open List Menu');
        }
      }

      /**
       * @param {'div'|'button'} type
       */
      function wrapIn(type) {
        if (type === 'div') {
          itemContainer = angular.element('<div class="md-no-style md-list-item-inner">');
          itemContainer.append(tElement.contents());
          tElement.addClass('md-proxy-focus');
        } else {
          // Element which holds the default list-item content.
          itemContainer = angular.element(
            '<div class="md-button md-no-style">' +
            '   <div class="md-list-item-inner"></div>' +
            '</div>'
          );

          // Button which shows ripple and executes primary action.
          var buttonWrap = angular.element(
            '<md-button class="md-no-style"></md-button>'
          );

          moveAttributes(tElement[0], buttonWrap[0]);

          // If there is no aria-label set on the button (previously copied over if present)
          // we determine the label from the content and copy it to the button.
          if (!buttonWrap.attr('aria-label')) {
            buttonWrap.attr('aria-label', $mdAria.getText(tElement));
          }

          // We allow developers to specify the `md-no-focus` class, to disable the focus style
          // on the button executor. Once more classes should be forwarded, we should probably make
          // the class forward more generic.
          if (tElement.hasClass('md-no-focus')) {
            buttonWrap.addClass('md-no-focus');
          }

          // Append the button wrap before our list-item content, because it will overlay in
          // relative.
          itemContainer.prepend(buttonWrap);
          itemContainer.children().eq(1).append(tElement.contents());

          tElement.addClass('_md-button-wrap');
        }

        tElement[0].setAttribute('tabindex', '-1');
        tElement.append(itemContainer);
      }

      function wrapSecondaryItems() {
        var secondaryItemsWrapper = angular.element('<div class="md-secondary-container">');

        angular.forEach(secondaryItems, function(secondaryItem) {
          wrapSecondaryItem(secondaryItem, secondaryItemsWrapper);
        });

        itemContainer.append(secondaryItemsWrapper);
      }

      /**
       * @param {HTMLElement} secondaryItem
       * @param container
       */
      function wrapSecondaryItem(secondaryItem, container) {
        // If the current secondary item is not a button, but contains a ng-click attribute,
        // the secondary item will be automatically wrapped inside of a button.
        if (secondaryItem && !isButton(secondaryItem) && secondaryItem.hasAttribute('ng-click')) {

          $mdAria.expect(secondaryItem, 'aria-label');
          var buttonWrapper = angular.element('<md-button class="md-secondary md-icon-button">');

          // Move the attributes from the secondary item to the generated button.
          // We also support some additional attributes from the secondary item,
          // because some developers may use a ngIf, ngHide, ngShow on their item.
          moveAttributes(secondaryItem, buttonWrapper[0], ['ng-if', 'ng-hide', 'ng-show']);

          secondaryItem.setAttribute('tabindex', '-1');
          buttonWrapper.append(secondaryItem);

          secondaryItem = buttonWrapper[0];
        }

        if (secondaryItem &&
            (!hasClickEvent(secondaryItem) ||
              (!tAttrs.ngClick && isProxiedElement(secondaryItem)))) {
          // In this case we remove the secondary class, so we can identify it later, when searching
          // for the proxy items.
          angular.element(secondaryItem).removeClass('md-secondary');
        }

        tElement.addClass('md-with-secondary');
        container.append(secondaryItem);
      }

      /**
       * Moves attributes from a source element to the destination element.
       * By default, the function will copy the most necessary attributes, supported
       * by the button executor for clickable list items.
       * @param source Element with the specified attributes
       * @param destination Element which will receive the attributes
       * @param extraAttrs Additional attributes, which will be moved over
       */
      function moveAttributes(source, destination, extraAttrs) {
        var copiedAttrs = $mdUtil.prefixer([
          'ng-if', 'ng-click', 'ng-dblclick', 'aria-label', 'ng-disabled', 'ui-sref',
          'href', 'ng-href', 'rel', 'target', 'ng-attr-ui-sref', 'ui-sref-opts', 'download'
        ]);

        if (extraAttrs) {
          copiedAttrs = copiedAttrs.concat($mdUtil.prefixer(extraAttrs));
        }

        angular.forEach(copiedAttrs, function(attr) {
          if (source.hasAttribute(attr)) {
            destination.setAttribute(attr, source.getAttribute(attr));
            source.removeAttribute(attr);
          }
        });
      }

      /**
       * @param {HTMLElement} element
       * @return {boolean} true if the element has one of the proxied tags, false otherwise
       */
      function isProxiedElement(element) {
        return proxiedTypes.indexOf(element.nodeName.toLowerCase()) !== -1;
      }

      /**
       * @param {HTMLElement} element
       * @return {boolean} true if the element is a button or md-button, false otherwise
       */
      function isButton(element) {
        var nodeName = element.nodeName.toUpperCase();

        return nodeName === "MD-BUTTON" || nodeName === "BUTTON";
      }

      /**
       * @param {Element} element
       * @return {boolean} true if the element has an ng-click attribute, false otherwise
       */
      function hasClickEvent(element) {
        var attr = element.attributes;
        for (var i = 0; i < attr.length; i++) {
          if (tAttrs.$normalize(attr[i].name) === 'ngClick') {
            return true;
          }
        }
        return false;
      }

      return postLink;

      function postLink($scope, $element, $attr, ctrl) {
        $element.addClass('_md');     // private md component indicator for styling

        var proxies       = [],
            firstElement  = $element[0].firstElementChild,
            isButtonWrap  = $element.hasClass('_md-button-wrap'),
            clickChild    = isButtonWrap ? firstElement.firstElementChild : firstElement,
            hasClick      = clickChild && hasClickEvent(clickChild),
            noProxies     = $element.hasClass('md-no-proxy');

        computeProxies();
        computeClickable();

        if (proxies.length) {
          angular.forEach(proxies, function(proxy) {
            proxy = angular.element(proxy);

            $scope.mouseActive = false;
            proxy.on('mousedown', function() {
              $scope.mouseActive = true;
              $timeout(function() {
                $scope.mouseActive = false;
              }, 100);
            })
            .on('focus', function() {
              if ($scope.mouseActive === false) { $element.addClass('md-focused'); }
              proxy.on('blur', function proxyOnBlur() {
                $element.removeClass('md-focused');
                proxy.off('blur', proxyOnBlur);
              });
            });
          });
        }

        function computeProxies() {
          if (firstElement && firstElement.children && !hasClick && !noProxies) {

            angular.forEach(proxiedTypes, function(type) {
              // All elements which are not capable of being used as a proxy have the .md-secondary
              // class applied. These items were identified in the secondary wrap function.
              angular.forEach(firstElement.querySelectorAll(type + ':not(.md-secondary)'), function(child) {
                proxies.push(child);
              });
            });
          }
        }

        function computeClickable() {
          if (proxies.length === 1 || hasClick) {
            $element.addClass('md-clickable');

            if (!hasClick) {
              ctrl.attachRipple($scope, angular.element($element[0].querySelector('.md-no-style')));
            }
          }
        }

        /**
         * @param {MouseEvent} event
         * @return {boolean}
         */
        function isEventFromControl(event) {
          var forbiddenControls = ['md-slider'];
          var eventBubblePath = $mdUtil.getEventPath(event);

          // If there is no bubble path, then the event was not bubbled.
          if (!eventBubblePath || eventBubblePath.length === 0) {
            return forbiddenControls.indexOf(event.target.tagName.toLowerCase()) !== -1;
          }

          // We iterate the event bubble path up and check for a possible component.
          // Our maximum index to search, is the list item root.
          var maxPath = eventBubblePath.indexOf($element.children()[0]);

          for (var i = 0; i < maxPath; i++) {
            if (forbiddenControls.indexOf(eventBubblePath[i].tagName.toLowerCase()) !== -1) {
              return true;
            }
          }
          return false;
        }

        /**
         * @param {KeyboardEvent} keypressEvent
         */
        var clickChildKeypressListener = function(keypressEvent) {
          if (keypressEvent.target.nodeName !== 'INPUT' &&
              keypressEvent.target.nodeName !== 'TEXTAREA' &&
              !keypressEvent.target.isContentEditable) {
            var keyCode = keypressEvent.which || keypressEvent.keyCode;
            if (keyCode === $mdConstant.KEY_CODE.SPACE) {
              if (clickChild) {
                clickChild.click();
                keypressEvent.preventDefault();
                keypressEvent.stopPropagation();
              }
            }
          }
        };

        if (!hasClick && !proxies.length) {
          clickChild && clickChild.addEventListener('keypress', clickChildKeypressListener);
        }

        $element.off('click');
        $element.off('keypress');
        // Disable ng-aria's "helpful" keydown event that causes our ng-click handlers to be called
        // twice.
        $element.off('keydown');

        if (proxies.length === 1 && clickChild) {
          $element.children().eq(0).on('click', function(clickEvent) {
            // When the event is coming from a control and it should not trigger the proxied element
            // then we are skipping.
            if (isEventFromControl(clickEvent)) return;

            var parentButton = $mdUtil.getClosest(clickEvent.target, 'BUTTON');
            if (!parentButton && clickChild.contains(clickEvent.target)) {
              angular.forEach(proxies, function(proxy) {
                if (clickEvent.target !== proxy && !proxy.contains(clickEvent.target)) {
                  if (proxy.nodeName === 'MD-MENU') {
                    proxy = proxy.children[0];
                  }
                  angular.element(proxy).triggerHandler('click');
                }
              });
            }
          });
        }

        $scope.$on('$destroy', function () {
          clickChild && clickChild.removeEventListener('keypress', clickChildKeypressListener);
        });
      }
    }
  };
}

/*
 * @private
 * @ngdoc controller
 * @name MdListController
 * @module material.components.list
 */
function MdListController($scope, $element, $mdListInkRipple) {
  var ctrl = this;
  ctrl.attachRipple = attachRipple;

  function attachRipple (scope, element) {
    var options = {};
    $mdListInkRipple.attach(scope, element, options);
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.menu
 */

angular.module('material.components.menu', [
  'material.core',
  'material.components.backdrop'
]);

})();
(function(){
"use strict";



MenuController.$inject = ["$mdMenu", "$attrs", "$element", "$scope", "$mdUtil", "$timeout", "$rootScope", "$q", "$log"];
angular
    .module('material.components.menu')
    .controller('mdMenuCtrl', MenuController);

/**
 * @ngInject
 */
function MenuController($mdMenu, $attrs, $element, $scope, $mdUtil, $timeout, $rootScope, $q, $log) {

  var prefixer = $mdUtil.prefixer();
  var menuContainer;
  var self = this;
  var triggerElement;

  this.nestLevel = parseInt($attrs.mdNestLevel, 10) || 0;

  /**
   * Called by our linking fn to provide access to the menu-content
   * element removed during link
   */
  this.init = function init(setMenuContainer, opts) {
    opts = opts || {};
    menuContainer = setMenuContainer;

    // Default element for ARIA attributes has the ngClick or ngMouseenter expression
    triggerElement = $element[0].querySelector(prefixer.buildSelector(['ng-click', 'ng-mouseenter']));
    triggerElement.setAttribute('aria-expanded', 'false');

    this.isInMenuBar = opts.isInMenuBar;
    this.mdMenuBarCtrl = opts.mdMenuBarCtrl;
    this.nestedMenus = $mdUtil.nodesToArray(menuContainer[0].querySelectorAll('.md-nested-menu'));

    menuContainer.on('$mdInterimElementRemove', function() {
      self.isOpen = false;
      $mdUtil.nextTick(function(){ self.onIsOpenChanged(self.isOpen);});
    });
    $mdUtil.nextTick(function(){ self.onIsOpenChanged(self.isOpen);});

    var menuContainerId = 'menu_container_' + $mdUtil.nextUid();
    menuContainer.attr('id', menuContainerId);
    angular.element(triggerElement).attr({
      'aria-owns': menuContainerId,
      'aria-haspopup': 'true'
    });

    $scope.$on('$destroy', angular.bind(this, function() {
      this.disableHoverListener();
      $mdMenu.destroy();
    }));

    menuContainer.on('$destroy', function() {
      $mdMenu.destroy();
    });
  };

  var openMenuTimeout, menuItems, deregisterScopeListeners = [];
  this.enableHoverListener = function() {
    deregisterScopeListeners.push($rootScope.$on('$mdMenuOpen', function(event, el) {
      if (menuContainer[0].contains(el[0])) {
        self.currentlyOpenMenu = el.controller('mdMenu');
        self.isAlreadyOpening = false;
        self.currentlyOpenMenu.registerContainerProxy(self.triggerContainerProxy.bind(self));
      }
    }));
    deregisterScopeListeners.push($rootScope.$on('$mdMenuClose', function(event, el) {
      if (menuContainer[0].contains(el[0])) {
        self.currentlyOpenMenu = undefined;
      }
    }));
    menuItems = angular.element($mdUtil.nodesToArray(menuContainer[0].children[0].children));
    menuItems.on('mouseenter', self.handleMenuItemHover);
    menuItems.on('mouseleave', self.handleMenuItemMouseLeave);
  };

  this.disableHoverListener = function() {
    while (deregisterScopeListeners.length) {
      deregisterScopeListeners.shift()();
    }
    menuItems && menuItems.off('mouseenter', self.handleMenuItemHover);
    menuItems && menuItems.off('mouseleave', self.handleMenuItemMouseLeave);
  };

  this.handleMenuItemHover = function(event) {
    if (self.isAlreadyOpening) return;
    var nestedMenu = (
      event.target.querySelector('md-menu')
        || $mdUtil.getClosest(event.target, 'MD-MENU')
    );
    openMenuTimeout = $timeout(function() {
      if (nestedMenu) {
        nestedMenu = angular.element(nestedMenu).controller('mdMenu');
      }

      if (self.currentlyOpenMenu && self.currentlyOpenMenu != nestedMenu) {
        var closeTo = self.nestLevel + 1;
        self.currentlyOpenMenu.close(true, { closeTo: closeTo });
        self.isAlreadyOpening = !!nestedMenu;
        nestedMenu && nestedMenu.open();
      } else if (nestedMenu && !nestedMenu.isOpen && nestedMenu.open) {
        self.isAlreadyOpening = !!nestedMenu;
        nestedMenu && nestedMenu.open();
      }
    }, nestedMenu ? 100 : 250);
    var focusableTarget = event.currentTarget.querySelector('.md-button:not([disabled])');
    focusableTarget && focusableTarget.focus();
  };

  this.handleMenuItemMouseLeave = function() {
    if (openMenuTimeout) {
      $timeout.cancel(openMenuTimeout);
      openMenuTimeout = undefined;
    }
  };


  /**
   * Uses the $mdMenu interim element service to open the menu contents
   */
  this.open = function openMenu(ev) {
    ev && ev.stopPropagation();
    ev && ev.preventDefault();
    if (self.isOpen) return;
    self.enableHoverListener();
    self.isOpen = true;
    $mdUtil.nextTick(function(){ self.onIsOpenChanged(self.isOpen);});
    triggerElement = triggerElement || (ev ? ev.target : $element[0]);
    triggerElement.setAttribute('aria-expanded', 'true');
    $scope.$emit('$mdMenuOpen', $element);
    $mdMenu.show({
      scope: $scope,
      mdMenuCtrl: self,
      nestLevel: self.nestLevel,
      element: menuContainer,
      target: triggerElement,
      preserveElement: true,
      parent: 'body'
    }).finally(function() {
      triggerElement.setAttribute('aria-expanded', 'false');
      self.disableHoverListener();
    });
  };

  this.onIsOpenChanged = function(isOpen) {
    if (isOpen) {
      menuContainer.attr('aria-hidden', 'false');
      $element[0].classList.add('md-open');
      angular.forEach(self.nestedMenus, function(el) {
        el.classList.remove('md-open');
      });
    } else {
      menuContainer.attr('aria-hidden', 'true');
      $element[0].classList.remove('md-open');
    }
    $scope.$mdMenuIsOpen = self.isOpen;
  };

  this.focusMenuContainer = function focusMenuContainer() {
    var focusTarget = menuContainer[0]
      .querySelector(prefixer.buildSelector(['md-menu-focus-target', 'md-autofocus']));

    if (!focusTarget) focusTarget = menuContainer[0].querySelector('.md-button:not([disabled])');
    focusTarget.focus();
  };

  this.registerContainerProxy = function registerContainerProxy(handler) {
    this.containerProxy = handler;
  };

  this.triggerContainerProxy = function triggerContainerProxy(ev) {
    this.containerProxy && this.containerProxy(ev);
  };

  this.destroy = function() {
    return self.isOpen ? $mdMenu.destroy() : $q.when(false);
  };

  // Use the $mdMenu interim element service to close the menu contents
  this.close = function closeMenu(skipFocus, closeOpts) {
    if (!self.isOpen) return;
    self.isOpen = false;
    $mdUtil.nextTick(function(){ self.onIsOpenChanged(self.isOpen);});

    var eventDetails = angular.extend({}, closeOpts, { skipFocus: skipFocus });
    $scope.$emit('$mdMenuClose', $element, eventDetails);
    $mdMenu.hide(null, closeOpts);

    if (!skipFocus) {
      var el = self.restoreFocusTo || $element.find('button')[0];
      if (el instanceof angular.element) el = el[0];
      if (el) el.focus();
    }
  };

  /**
   * Build a nice object out of our string attribute which specifies the
   * target mode for left and top positioning
   */
  this.positionMode = function positionMode() {
    var attachment = ($attrs.mdPositionMode || 'target').split(' ');

    // If attachment is a single item, duplicate it for our second value.
    // ie. 'target' -> 'target target'
    if (attachment.length === 1) {
      attachment.push(attachment[0]);
    }

    return {
      left: attachment[0],
      top: attachment[1]
    };
  };

  /**
   * Build a nice object out of our string attribute which specifies
   * the offset of top and left in pixels.
   */
  this.offsets = function offsets() {
    var position = ($attrs.mdOffset || '0 0').split(' ').map(parseFloat);
    if (position.length === 2) {
      return {
        left: position[0],
        top: position[1]
      };
    } else if (position.length === 1) {
      return {
        top: position[0],
        left: position[0]
      };
    } else {
      throw Error('Invalid offsets specified. Please follow format <x, y> or <n>');
    }
  };

  // Functionality that is exposed in the view.
  $scope.$mdMenu = {
    open: this.open,
    close: this.close
  };

  // Deprecated APIs
  $scope.$mdOpenMenu = angular.bind(this, function() {
    $log.warn('mdMenu: The $mdOpenMenu method is deprecated. Please use `$mdMenu.open`.');
    return this.open.apply(this, arguments);
  });
}

})();
(function(){
"use strict";

/**
 * @ngdoc directive
 * @name mdMenu
 * @module material.components.menu
 * @restrict E
 * @description
 *
 * Menus are elements that open when clicked. They are useful for displaying
 * additional options within the context of an action.
 *
 * Every `md-menu` must specify exactly two child elements. The first element is what is
 * left in the DOM and is used to open the menu. This element is called the trigger element.
 * The trigger element's scope has access to `$mdMenu.open($event)`
 * which it may call to open the menu. By passing $event as argument, the
 * corresponding event is stopped from propagating up the DOM-tree. Similarly, `$mdMenu.close()`
 * can be used to close the menu.
 *
 * The second element is the `md-menu-content` element which represents the
 * contents of the menu when it is open. Typically this will contain `md-menu-item`s,
 * but you can do custom content as well.
 *
 * <hljs lang="html">
 * <md-menu>
 *  <!-- Trigger element is a md-button with an icon -->
 *  <md-button ng-click="$mdMenu.open($event)" class="md-icon-button" aria-label="Open sample menu">
 *    <md-icon md-svg-icon="call:phone"></md-icon>
 *  </md-button>
 *  <md-menu-content>
 *    <md-menu-item><md-button ng-click="doSomething()">Do Something</md-button></md-menu-item>
 *  </md-menu-content>
 * </md-menu>
 * </hljs>

 * ## Sizing Menus
 *
 * The width of the menu when it is open may be specified by specifying a `width`
 * attribute on the `md-menu-content` element.
 * See the [Material Design Spec](https://material.io/archive/guidelines/components/menus.html#menus-simple-menus)
 * for more information.
 *
 * ## Menu Density
 *
 * You can use dense menus by adding the `md-dense` class to the `md-menu-content` element.
 * This reduces the height of menu items, their top and bottom padding, and default font size.
 * Without the `md-dense` class, we use the "mobile" height of `48px`. With the `md-dense` class,
 * we use the "desktop" height of `32px`. We do not support the "dense desktop" option in the spec,
 * which uses a height of `24px`, at this time.
 * See the [Menu Specs](https://material.io/archive/guidelines/components/menus.html#menus-specs)
 * section of the Material Design Spec for more information.
 *
 * ## Aligning Menus
 *
 * When a menu opens, it is important that the content aligns with the trigger element.
 * Failure to align menus can result in jarring experiences for users as content
 * suddenly shifts. To help with this, `md-menu` provides several APIs to help
 * with alignment.
 *
 * ### Target Mode
 *
 * By default, `md-menu` will attempt to align the `md-menu-content` by aligning
 * designated child elements in both the trigger and the menu content.
 *
 * To specify the alignment element in the `trigger` you can use the `md-menu-origin`
 * attribute on a child element. If no `md-menu-origin` is specified, the `md-menu`
 * will be used as the origin element.
 *
 * Similarly, the `md-menu-content` may specify a `md-menu-align-target` for a
 * `md-menu-item` to specify the node that it should try and align with.
 *
 * In this example code, we specify an icon to be our origin element, and an
 * icon in our menu content to be our alignment target. This ensures that both
 * icons are aligned when the menu opens.
 *
 * <hljs lang="html">
 * <md-menu>
 *  <md-button ng-click="$mdMenu.open($event)" class="md-icon-button" aria-label="Open some menu">
 *    <md-icon md-menu-origin md-svg-icon="call:phone"></md-icon>
 *  </md-button>
 *  <md-menu-content>
 *    <md-menu-item>
 *      <md-button ng-click="doSomething()" aria-label="Do something">
 *        <md-icon md-menu-align-target md-svg-icon="call:phone"></md-icon>
 *        Do Something
 *      </md-button>
 *    </md-menu-item>
 *  </md-menu-content>
 * </md-menu>
 * </hljs>
 *
 * ### Position Mode
 *
 * We can specify the origin of the menu by using the `md-position-mode` attribute.
 * This attribute allows specifying the positioning by the `x` and `y` axes.
 *
 * The default mode is `target target`. This mode uses the left and top edges of the origin element
 * to position the menu in LTR layouts. The `x` axis modes will adjust when in RTL layouts.
 *
 * Sometimes you want to specify alignment from the right side of a origin element. For example,
 * if we have a menu on the right side a toolbar, we may want to right align our menu content.
 * We can use `target-right target` to specify a right-oriented alignment target.
 * There is a working example of this in the Menu Position Modes demo.
 *
 * #### Horizontal Positioning Options
 * - `target`
 * - `target-left`
 * - `target-right`
 * - `cascade`
 * - `right`
 * - `left`
 *
 * #### Vertical Positioning Options
 * - `target`
 * - `cascade`
 * - `bottom`
 *
 * ### Menu Offsets
 *
 * It is sometimes unavoidable to need to have a deeper level of control for
 * the positioning of a menu to ensure perfect alignment. `md-menu` provides
 * the `md-offset` attribute to allow pixel-level specificity when adjusting
 * menu positioning.
 *
 * This offset is provided in the format of `x y` or `n` where `n` will be used
 * in both the `x` and `y` axis.
 * For example, to move a menu by `2px` down from the top, we can use:
 *
 * <hljs lang="html">
 * <md-menu md-offset="0 2">
 *   <!-- menu-content -->
 * </md-menu>
 * </hljs>
 *
 * Specifying `md-offset="2 2"` would shift the menu two pixels down and two pixels to the right.
 *
 * ### Auto Focus
 * By default, when a menu opens, `md-menu` focuses the first button in the menu content.
 *
 * Sometimes you would like to focus another menu item instead of the first.<br/>
 * This can be done by applying the `md-autofocus` directive on the given element.
 *
 * <hljs lang="html">
 * <md-menu-item>
 *   <md-button md-autofocus ng-click="doSomething()">
 *     Auto Focus
 *   </md-button>
 * </md-menu-item>
 * </hljs>
 *
 *
 * ### Preventing close
 *
 * Sometimes you would like to be able to click on a menu item without having the menu
 * close. To do this, AngularJS Material exposes the `md-prevent-menu-close` attribute which
 * can be added to a button inside a menu to stop the menu from automatically closing.
 * You can then close the menu either by using `$mdMenu.close()` in the template,
 * or programmatically by injecting `$mdMenu` and calling `$mdMenu.hide()`.
 *
 * <hljs lang="html">
 * <md-menu-content ng-mouseleave="$mdMenu.close()">
 *   <md-menu-item>
 *     <md-button ng-click="doSomething()" aria-label="Do something" md-prevent-menu-close="md-prevent-menu-close">
 *       <md-icon md-menu-align-target md-svg-icon="call:phone"></md-icon>
 *       Do Something
 *     </md-button>
 *   </md-menu-item>
 * </md-menu-content>
 * </hljs>
 *
 * @usage
 * <hljs lang="html">
 * <md-menu>
 *  <md-button ng-click="$mdMenu.open($event)" class="md-icon-button">
 *    <md-icon md-svg-icon="call:phone"></md-icon>
 *  </md-button>
 *  <md-menu-content>
 *    <md-menu-item><md-button ng-click="doSomething()">Do Something</md-button></md-menu-item>
 *  </md-menu-content>
 * </md-menu>
 * </hljs>
 *
 * @param {string=} md-position-mode Specify pre-defined position modes for the `x` and `y` axes.
 *  The default modes are `target target`. This positions the origin of the menu using the left and top edges
 *  of the origin element in LTR layouts.<br>
 *  #### Valid modes for horizontal positioning
 * - `target`
 * - `target-left`
 * - `target-right`
 * - `cascade`
 * - `right`
 * - `left`<br>
 *  #### Valid modes for vertical positioning
 * - `target`
 * - `cascade`
 * - `bottom`
 * @param {string=} md-offset An offset to apply to the dropdown on opening, after positioning.
 *  Defined as `x` and `y` pixel offset values in the form of `x y`.<br>
 *  The default value is `0 0`.
 */
MenuDirective.$inject = ["$mdUtil"];
angular
    .module('material.components.menu')
    .directive('mdMenu', MenuDirective);

/**
 * @ngInject
 */
function MenuDirective($mdUtil) {
  var INVALID_PREFIX = 'Invalid HTML for md-menu: ';
  return {
    restrict: 'E',
    require: ['mdMenu', '?^mdMenuBar'],
    controller: 'mdMenuCtrl', // empty function to be built by link
    scope: true,
    compile: compile
  };

  function compile(templateElement) {
    templateElement.addClass('md-menu');

    var triggerEl = templateElement.children()[0];
    var prefixer = $mdUtil.prefixer();

    if (!prefixer.hasAttribute(triggerEl, 'ng-click')) {
      triggerEl = triggerEl
          .querySelector(prefixer.buildSelector(['ng-click', 'ng-mouseenter'])) || triggerEl;
    }

    var isButtonTrigger = triggerEl.nodeName === 'MD-BUTTON' || triggerEl.nodeName === 'BUTTON';

    if (triggerEl && isButtonTrigger && !triggerEl.hasAttribute('type')) {
      triggerEl.setAttribute('type', 'button');
    }

    if (!triggerEl) {
      throw Error(INVALID_PREFIX + 'Expected the menu to have a trigger element.');
    }

    if (templateElement.children().length !== 2) {
      throw Error(INVALID_PREFIX + 'Expected two children elements. The second element must have a `md-menu-content` element.');
    }

    // Default element for ARIA attributes has the ngClick or ngMouseenter expression
    triggerEl && triggerEl.setAttribute('aria-haspopup', 'true');

    var nestedMenus = templateElement[0].querySelectorAll('md-menu');
    var nestingDepth = parseInt(templateElement[0].getAttribute('md-nest-level'), 10) || 0;
    if (nestedMenus) {
      angular.forEach($mdUtil.nodesToArray(nestedMenus), function(menuEl) {
        if (!menuEl.hasAttribute('md-position-mode')) {
          menuEl.setAttribute('md-position-mode', 'cascade');
        }
        menuEl.classList.add('_md-nested-menu');
        menuEl.setAttribute('md-nest-level', nestingDepth + 1);
      });
    }
    return link;
  }

  function link(scope, element, attr, ctrls) {
    var mdMenuCtrl = ctrls[0];
    var isInMenuBar = !!ctrls[1];
    var mdMenuBarCtrl = ctrls[1];
    // Move everything into a md-menu-container and pass it to the controller
    var menuContainer = angular.element('<div class="_md md-open-menu-container md-whiteframe-z2"></div>');
    var menuContents = element.children()[1];

    element.addClass('_md');     // private md component indicator for styling

    if (!menuContents.hasAttribute('role')) {
      menuContents.setAttribute('role', 'menu');
    }
    menuContainer.append(menuContents);

    element.on('$destroy', function() {
      menuContainer.remove();
    });

    element.append(menuContainer);
    menuContainer[0].style.display = 'none';
    mdMenuCtrl.init(menuContainer, { isInMenuBar: isInMenuBar, mdMenuBarCtrl: mdMenuBarCtrl });
  }
}

})();
(function(){
"use strict";


MenuProvider.$inject = ["$$interimElementProvider"];angular
  .module('material.components.menu')
  .provider('$mdMenu', MenuProvider);

/**
 * Interim element provider for the menu.
 * Handles behavior for a menu while it is open, including:
 *    - handling animating the menu opening/closing
 *    - handling key/mouse events on the menu element
 *    - handling enabling/disabling scroll while the menu is open
 *    - handling redrawing during resizes and orientation changes
 *
 */

function MenuProvider($$interimElementProvider) {
  menuDefaultOptions.$inject = ["$mdUtil", "$mdTheming", "$mdConstant", "$document", "$window", "$q", "$$rAF", "$animateCss", "$animate", "$log"];
  var MENU_EDGE_MARGIN = 8;

  return $$interimElementProvider('$mdMenu')
    .setDefaults({
      methods: ['target'],
      options: menuDefaultOptions
    });

  /* @ngInject */
  function menuDefaultOptions($mdUtil, $mdTheming, $mdConstant, $document, $window, $q, $$rAF,
                              $animateCss, $animate, $log) {

    var prefixer = $mdUtil.prefixer();
    var animator = $mdUtil.dom.animator;

    return {
      parent: 'body',
      onShow: onShow,
      onRemove: onRemove,
      hasBackdrop: true,
      disableParentScroll: true,
      skipCompile: true,
      preserveScope: true,
      multiple: true,
      themable: true
    };

    /**
     * Show modal backdrop element...
     * @returns {function(): void} A function that removes this backdrop
     */
    function showBackdrop(scope, element, options) {
      if (options.nestLevel) return angular.noop;

      // If we are not within a dialog...
      if (options.disableParentScroll && !$mdUtil.getClosest(options.target, 'MD-DIALOG')) {
        // !! DO this before creating the backdrop; since disableScrollAround()
        //    configures the scroll offset; which is used by mdBackDrop postLink()
        options.restoreScroll = $mdUtil.disableScrollAround(options.element, options.parent);
      } else {
        options.disableParentScroll = false;
      }

      if (options.hasBackdrop) {
        options.backdrop = $mdUtil.createBackdrop(scope, "md-menu-backdrop md-click-catcher");

        $animate.enter(options.backdrop, $document[0].body);
      }

      /**
       * Hide and destroys the backdrop created by showBackdrop()
       */
      return function hideBackdrop() {
        if (options.backdrop) options.backdrop.remove();
        if (options.disableParentScroll) options.restoreScroll();
      };
    }

    /**
     * Removing the menu element from the DOM and remove all associated event listeners
     * and backdrop
     */
    function onRemove(scope, element, opts) {
      opts.cleanupInteraction();
      opts.cleanupBackdrop();
      opts.cleanupResizing();
      opts.hideBackdrop();

      // Before the menu is closing remove the clickable class.
      element.removeClass('md-clickable');

      // For navigation $destroy events, do a quick, non-animated removal,
      // but for normal closes (from clicks, etc) animate the removal

      return (opts.$destroy === true) ? detachAndClean() : animateRemoval().then(detachAndClean);

      /**
       * For normal closes, animate the removal.
       * For forced closes (like $destroy events), skip the animations
       */
      function animateRemoval() {
        return $animateCss(element, {addClass: 'md-leave'}).start();
      }

      /**
       * Detach the element
       */
      function detachAndClean() {
        element.removeClass('md-active');
        detachElement(element, opts);
        opts.alreadyOpen = false;
      }

    }

    /**
     * Inserts and configures the staged Menu element into the DOM, positioning it,
     * and wiring up various interaction events
     */
    function onShow(scope, element, opts) {
      sanitizeAndConfigure(opts);

      if (opts.menuContentEl[0]) {
        // Inherit the theme from the target element.
        $mdTheming.inherit(opts.menuContentEl, opts.target);
      } else {
        $log.warn(
          '$mdMenu: Menu elements should always contain a `md-menu-content` element,' +
          'otherwise interactivity features will not work properly.',
          element
        );
      }

      // Register various listeners to move menu on resize/orientation change
      opts.cleanupResizing = startRepositioningOnResize();
      opts.hideBackdrop = showBackdrop(scope, element, opts);

      // Return the promise for when our menu is done animating in
      return showMenu()
        .then(function(response) {
          opts.alreadyOpen = true;
          opts.cleanupInteraction = activateInteraction();
          opts.cleanupBackdrop = setupBackdrop();

          // Since the menu finished its animation, mark the menu as clickable.
          element.addClass('md-clickable');

          return response;
        });

      /**
       * Place the menu into the DOM and call positioning related functions
       */
      function showMenu() {
        opts.parent.append(element);
        element[0].style.display = '';

        return $q(function(resolve) {
          var position = calculateMenuPosition(element, opts);

          element.removeClass('md-leave');

          // Animate the menu scaling, and opacity [from its position origin (default == top-left)]
          // to normal scale.
          $animateCss(element, {
            addClass: 'md-active',
            from: animator.toCss(position),
            to: animator.toCss({transform: ''})
          })
          .start()
          .then(resolve);

        });
      }

      /**
       * Check for valid opts and set some sane defaults
       */
      function sanitizeAndConfigure() {
        if (!opts.target) {
          throw Error(
            '$mdMenu.show() expected a target to animate from in options.target'
          );
        }
        angular.extend(opts, {
          alreadyOpen: false,
          isRemoved: false,
          target: angular.element(opts.target), // make sure it's not a naked DOM node
          parent: angular.element(opts.parent),
          menuContentEl: angular.element(element[0].querySelector('md-menu-content'))
        });
      }

      /**
       * Configure various resize listeners for screen changes
       */
      function startRepositioningOnResize() {

        var repositionMenu = (function(target, options) {
          return $$rAF.throttle(function() {
            if (opts.isRemoved) return;
            var position = calculateMenuPosition(target, options);

            target.css(animator.toCss(position));
          });
        })(element, opts);

        $window.addEventListener('resize', repositionMenu);
        $window.addEventListener('orientationchange', repositionMenu);

        return function stopRepositioningOnResize() {

          // Disable resizing handlers
          $window.removeEventListener('resize', repositionMenu);
          $window.removeEventListener('orientationchange', repositionMenu);

        };
      }

      /**
       * Sets up the backdrop and listens for click elements.
       * Once the backdrop will be clicked, the menu will automatically close.
       * @returns {!Function} Function to remove the backdrop.
       */
      function setupBackdrop() {
        if (!opts.backdrop) return angular.noop;

        opts.backdrop.on('click', onBackdropClick);

        return function() {
          opts.backdrop.off('click', onBackdropClick);
        };
      }

      /**
       * Function to be called whenever the backdrop is clicked.
       * @param {!MouseEvent} event
       */
      function onBackdropClick(event) {
        event.preventDefault();
        event.stopPropagation();

        scope.$apply(function() {
          opts.mdMenuCtrl.close(true, { closeAll: true });
        });
      }

      /**
       * Activate interaction on the menu. Resolves the focus target and closes the menu on
       * escape or option click.
       * @returns {!Function} Function to deactivate the interaction listeners.
       */
      function activateInteraction() {
        if (!opts.menuContentEl[0]) return angular.noop;

        // Wire up keyboard listeners.
        // - Close on escape,
        // - focus next item on down arrow,
        // - focus prev item on up
        opts.menuContentEl.on('keydown', onMenuKeyDown);
        opts.menuContentEl[0].addEventListener('click', captureClickListener, true);

        // kick off initial focus in the menu on the first enabled element
        var focusTarget = opts.menuContentEl[0]
          .querySelector(prefixer.buildSelector(['md-menu-focus-target', 'md-autofocus']));

        if (!focusTarget) {
          var childrenLen = opts.menuContentEl[0].children.length;
          for (var childIndex = 0; childIndex < childrenLen; childIndex++) {
            var child = opts.menuContentEl[0].children[childIndex];
            focusTarget = child.querySelector('.md-button:not([disabled])');
            if (focusTarget) {
              break;
            }
            if (child.firstElementChild && !child.firstElementChild.disabled) {
              focusTarget = child.firstElementChild;
              break;
            }
          }
        }

        focusTarget && focusTarget.focus();

        return function cleanupInteraction() {
          opts.menuContentEl.off('keydown', onMenuKeyDown);
          opts.menuContentEl[0].removeEventListener('click', captureClickListener, true);
        };

        // ************************************
        // internal functions
        // ************************************

        function onMenuKeyDown(ev) {
          var handled;
          switch (ev.keyCode) {
            case $mdConstant.KEY_CODE.ESCAPE:
              if (opts.nestLevel) {
                opts.mdMenuCtrl.close();
              } else {
                opts.mdMenuCtrl.close(false, { closeAll: true });
              }
              handled = true;
              break;
            case $mdConstant.KEY_CODE.TAB:
              opts.mdMenuCtrl.close(false, { closeAll: true });
              // Don't prevent default or stop propagation on this event as we want tab
              // to move the focus to the next focusable element on the page.
              handled = false;
              break;
            case $mdConstant.KEY_CODE.UP_ARROW:
              if (!focusMenuItem(ev, opts.menuContentEl, opts, -1) && !opts.nestLevel) {
                opts.mdMenuCtrl.triggerContainerProxy(ev);
              }
              handled = true;
              break;
            case $mdConstant.KEY_CODE.DOWN_ARROW:
              if (!focusMenuItem(ev, opts.menuContentEl, opts, 1) && !opts.nestLevel) {
                opts.mdMenuCtrl.triggerContainerProxy(ev);
              }
              handled = true;
              break;
            case $mdConstant.KEY_CODE.LEFT_ARROW:
              if (opts.nestLevel) {
                opts.mdMenuCtrl.close();
              } else {
                opts.mdMenuCtrl.triggerContainerProxy(ev);
              }
              handled = true;
              break;
            case $mdConstant.KEY_CODE.RIGHT_ARROW:
              var parentMenu = $mdUtil.getClosest(ev.target, 'MD-MENU');
              if (parentMenu && parentMenu != opts.parent[0]) {
                ev.target.click();
              } else {
                opts.mdMenuCtrl.triggerContainerProxy(ev);
              }
              handled = true;
              break;
          }
          if (handled) {
            ev.preventDefault();
            ev.stopImmediatePropagation();
          }
        }

        function onBackdropClick(e) {
          e.preventDefault();
          e.stopPropagation();
          scope.$apply(function() {
            opts.mdMenuCtrl.close(true, { closeAll: true });
          });
        }

        // Close menu on menu item click, if said menu-item is not disabled
        function captureClickListener(e) {
          var target = e.target;
          // Traverse up the event until we get to the menuContentEl to see if
          // there is an ng-click and that the ng-click is not disabled
          do {
            if (target == opts.menuContentEl[0]) return;
            if ((hasAnyAttribute(target, ['ng-click', 'ng-href', 'ui-sref']) ||
                target.nodeName == 'BUTTON' || target.nodeName == 'MD-BUTTON') && !hasAnyAttribute(target, ['md-prevent-menu-close'])) {
              var closestMenu = $mdUtil.getClosest(target, 'MD-MENU');
              if (!target.hasAttribute('disabled') && (!closestMenu || closestMenu == opts.parent[0])) {
                close();
              }
              break;
            }
          } while (target = target.parentNode);

          function close() {
            scope.$apply(function() {
              opts.mdMenuCtrl.close(true, { closeAll: true });
            });
          }

          function hasAnyAttribute(target, attrs) {
            if (!target) return false;

            for (var i = 0, attr; attr = attrs[i]; ++i) {
              if (prefixer.hasAttribute(target, attr)) {
                return true;
              }
            }

            return false;
          }
        }

      }
    }

    /**
     * Takes a keypress event and focuses the next/previous menu
     * item from the emitting element
     * @param {event} e - The origin keypress event
     * @param {angular.element} menuEl - The menu element
     * @param {object} opts - The interim element options for the mdMenu
     * @param {number} direction - The direction to move in (+1 = next, -1 = prev)
     */
    function focusMenuItem(e, menuEl, opts, direction) {
      var currentItem = $mdUtil.getClosest(e.target, 'MD-MENU-ITEM');

      var items = $mdUtil.nodesToArray(menuEl[0].children);
      var currentIndex = items.indexOf(currentItem);

      // Traverse through our elements in the specified direction (+/-1) and try to
      // focus them until we find one that accepts focus
      var didFocus;
      for (var i = currentIndex + direction; i >= 0 && i < items.length; i = i + direction) {
        var focusTarget = items[i].querySelector('.md-button');
        didFocus = attemptFocus(focusTarget);
        if (didFocus) {
          break;
        }
      }
      return didFocus;
    }

    /**
     * Attempts to focus an element. Checks whether that element is the currently
     * focused element after attempting.
     * @param {HTMLElement} el - the element to attempt focus on
     * @returns {boolean} - whether the element was successfully focused
     */
    function attemptFocus(el) {
      if (el && el.getAttribute('tabindex') != -1) {
        el.focus();
        return ($document[0].activeElement == el);
      }
    }

    /**
     * Use browser to remove this element without triggering a $destroy event
     */
    function detachElement(element, opts) {
      if (!opts.preserveElement) {
        if (toNode(element).parentNode === toNode(opts.parent)) {
          toNode(opts.parent).removeChild(toNode(element));
        }
      } else {
        toNode(element).style.display = 'none';
      }
    }

    /**
     * Computes menu position and sets the style on the menu container
     * @param {HTMLElement} el - the menu container element
     * @param {object} opts - the interim element options object
     */
    function calculateMenuPosition(el, opts) {

      var containerNode = el[0],
        openMenuNode = el[0].firstElementChild,
        openMenuNodeRect = openMenuNode.getBoundingClientRect(),
        boundryNode = $document[0].body,
        boundryNodeRect = boundryNode.getBoundingClientRect();

      var menuStyle = $window.getComputedStyle(openMenuNode);

      var originNode = opts.target[0].querySelector(prefixer.buildSelector('md-menu-origin')) || opts.target[0],
        originNodeRect = originNode.getBoundingClientRect();

      var bounds = {
        left: boundryNodeRect.left + MENU_EDGE_MARGIN,
        top: Math.max(boundryNodeRect.top, 0) + MENU_EDGE_MARGIN,
        bottom: Math.max(boundryNodeRect.bottom, Math.max(boundryNodeRect.top, 0) + boundryNodeRect.height) - MENU_EDGE_MARGIN,
        right: boundryNodeRect.right - MENU_EDGE_MARGIN
      };

      var alignTarget, alignTargetRect = { top:0, left : 0, right:0, bottom:0 }, existingOffsets  = { top:0, left : 0, right:0, bottom:0  };
      var positionMode = opts.mdMenuCtrl.positionMode();

      if (positionMode.top === 'target' || positionMode.left === 'target' || positionMode.left === 'target-right') {
        alignTarget = firstVisibleChild();
        if (alignTarget) {
          // TODO: Allow centering on an arbitrary node, for now center on first menu-item's child
          alignTarget = alignTarget.firstElementChild || alignTarget;
          alignTarget = alignTarget.querySelector(prefixer.buildSelector('md-menu-align-target')) || alignTarget;
          alignTargetRect = alignTarget.getBoundingClientRect();

          existingOffsets = {
            top: parseFloat(containerNode.style.top || 0),
            left: parseFloat(containerNode.style.left || 0)
          };
        }
      }

      var position = {};
      var transformOrigin = 'top ';

      switch (positionMode.top) {
        case 'target':
          position.top = existingOffsets.top + originNodeRect.top - alignTargetRect.top;
          break;
        case 'cascade':
          position.top = originNodeRect.top - parseFloat(menuStyle.paddingTop) - originNode.style.top;
          break;
        case 'bottom':
          position.top = originNodeRect.top + originNodeRect.height;
          break;
        default:
          throw new Error('Invalid target mode "' + positionMode.top + '" specified for md-menu on Y axis.');
      }

      var rtl = $mdUtil.isRtl(el);

      switch (positionMode.left) {
        case 'target':
          position.left = existingOffsets.left + originNodeRect.left - alignTargetRect.left;
          transformOrigin += rtl ? 'right'  : 'left';
          break;
        case 'target-left':
          position.left = originNodeRect.left;
          transformOrigin += 'left';
          break;
        case 'target-right':
          position.left = originNodeRect.right - openMenuNodeRect.width + (openMenuNodeRect.right - alignTargetRect.right);
          transformOrigin += 'right';
          break;
        case 'cascade':
          var willFitRight = rtl ? (originNodeRect.left - openMenuNodeRect.width) < bounds.left : (originNodeRect.right + openMenuNodeRect.width) < bounds.right;
          position.left = willFitRight ? originNodeRect.right - originNode.style.left : originNodeRect.left - originNode.style.left - openMenuNodeRect.width;
          transformOrigin += willFitRight ? 'left' : 'right';
          break;
        case 'right':
          if (rtl) {
            position.left = originNodeRect.right - originNodeRect.width;
            transformOrigin += 'left';
          } else {
            position.left = originNodeRect.right - openMenuNodeRect.width;
            transformOrigin += 'right';
          }
          break;
        case 'left':
          if (rtl) {
            position.left = originNodeRect.right - openMenuNodeRect.width;
            transformOrigin += 'right';
          } else {
            position.left = originNodeRect.left;
            transformOrigin += 'left';
          }
          break;
        default:
          throw new Error('Invalid target mode "' + positionMode.left + '" specified for md-menu on X axis.');
      }

      var offsets = opts.mdMenuCtrl.offsets();
      position.top += offsets.top;
      position.left += offsets.left;

      clamp(position);

      var scaleX = Math.round(100 * Math.min(originNodeRect.width / containerNode.offsetWidth, 1.0)) / 100;
      var scaleY = Math.round(100 * Math.min(originNodeRect.height / containerNode.offsetHeight, 1.0)) / 100;

      return {
        top: Math.round(position.top),
        left: Math.round(position.left),
        // Animate a scale out if we aren't just repositioning
        transform: !opts.alreadyOpen ? $mdUtil.supplant('scale({0},{1})', [scaleX, scaleY]) : undefined,
        transformOrigin: transformOrigin
      };

      /**
       * Clamps the repositioning of the menu within the confines of
       * bounding element (often the screen/body)
       */
      function clamp(pos) {
        pos.top = Math.max(Math.min(pos.top, bounds.bottom - containerNode.offsetHeight), bounds.top);
        pos.left = Math.max(Math.min(pos.left, bounds.right - containerNode.offsetWidth), bounds.left);
      }

      /**
       * Gets the first visible child in the openMenuNode
       * Necessary incase menu nodes are being dynamically hidden
       */
      function firstVisibleChild() {
        for (var i = 0; i < openMenuNode.children.length; ++i) {
          if ($window.getComputedStyle(openMenuNode.children[i]).display != 'none') {
            return openMenuNode.children[i];
          }
        }
      }
    }
  }
  function toNode(el) {
    if (el instanceof angular.element) {
      el = el[0];
    }
    return el;
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.menuBar
 */

angular.module('material.components.menuBar', [
  'material.core',
  'material.components.icon',
  'material.components.menu'
]);

})();
(function(){
"use strict";


MenuBarController.$inject = ["$scope", "$rootScope", "$element", "$attrs", "$mdConstant", "$document", "$mdUtil", "$timeout"];
angular
  .module('material.components.menuBar')
  .controller('MenuBarController', MenuBarController);

var BOUND_MENU_METHODS = ['handleKeyDown', 'handleMenuHover', 'scheduleOpenHoveredMenu', 'cancelScheduledOpen'];

/**
 * @ngInject
 */
function MenuBarController($scope, $rootScope, $element, $attrs, $mdConstant, $document, $mdUtil, $timeout) {
  this.$element = $element;
  this.$attrs = $attrs;
  this.$mdConstant = $mdConstant;
  this.$mdUtil = $mdUtil;
  this.$document = $document;
  this.$scope = $scope;
  this.$rootScope = $rootScope;
  this.$timeout = $timeout;

  var self = this;
  angular.forEach(BOUND_MENU_METHODS, function(methodName) {
    self[methodName] = angular.bind(self, self[methodName]);
  });
}

MenuBarController.prototype.init = function() {
  var $element = this.$element;
  var $mdUtil = this.$mdUtil;
  var $scope = this.$scope;

  var self = this;
  var deregisterFns = [];
  $element.on('keydown', this.handleKeyDown);
  this.parentToolbar = $mdUtil.getClosest($element, 'MD-TOOLBAR');

  deregisterFns.push(this.$rootScope.$on('$mdMenuOpen', function(event, el) {
    if (self.getMenus().indexOf(el[0]) != -1) {
      $element[0].classList.add('md-open');
      el[0].classList.add('md-open');
      self.currentlyOpenMenu = el.controller('mdMenu');
      self.currentlyOpenMenu.registerContainerProxy(self.handleKeyDown);
      self.enableOpenOnHover();
    }
  }));

  deregisterFns.push(this.$rootScope.$on('$mdMenuClose', function(event, el, opts) {
    var rootMenus = self.getMenus();
    if (rootMenus.indexOf(el[0]) != -1) {
      $element[0].classList.remove('md-open');
      el[0].classList.remove('md-open');
    }

    var ctrl = angular.element(el[0]).controller('mdMenu');
    if (ctrl.isInMenuBar && ctrl.mdMenuBarCtrl === self) {
      var parentMenu = el[0];
      while (parentMenu && rootMenus.indexOf(parentMenu) == -1) {
        parentMenu = $mdUtil.getClosest(parentMenu, 'MD-MENU', true);
      }
      if (parentMenu) {
        if (!opts.skipFocus) parentMenu.querySelector('button:not([disabled])').focus();
        self.currentlyOpenMenu = undefined;
      }
      self.disableOpenOnHover();
      self.setKeyboardMode(true);
    }
  }));

  $scope.$on('$destroy', function() {
    self.disableOpenOnHover();
    while (deregisterFns.length) {
      deregisterFns.shift()();
    }
  });


  this.setKeyboardMode(true);
};

MenuBarController.prototype.setKeyboardMode = function(enabled) {
  if (enabled) this.$element[0].classList.add('md-keyboard-mode');
  else this.$element[0].classList.remove('md-keyboard-mode');
};

MenuBarController.prototype.enableOpenOnHover = function() {
  if (this.openOnHoverEnabled) return;

  var self = this;

  self.openOnHoverEnabled = true;

  if (self.parentToolbar) {
    self.parentToolbar.classList.add('md-has-open-menu');

    // Needs to be on the next tick so it doesn't close immediately.
    self.$mdUtil.nextTick(function() {
      angular.element(self.parentToolbar).on('click', self.handleParentClick);
    }, false);
  }

  angular
    .element(self.getMenus())
    .on('mouseenter', self.handleMenuHover);
};

MenuBarController.prototype.handleMenuHover = function(e) {
  this.setKeyboardMode(false);
  if (this.openOnHoverEnabled) {
    this.scheduleOpenHoveredMenu(e);
  }
};

MenuBarController.prototype.disableOpenOnHover = function() {
  if (!this.openOnHoverEnabled) return;

  this.openOnHoverEnabled = false;

  if (this.parentToolbar) {
    this.parentToolbar.classList.remove('md-has-open-menu');
    angular.element(this.parentToolbar).off('click', this.handleParentClick);
  }

  angular
    .element(this.getMenus())
    .off('mouseenter', this.handleMenuHover);
};

MenuBarController.prototype.scheduleOpenHoveredMenu = function(e) {
  var menuEl = angular.element(e.currentTarget);
  var menuCtrl = menuEl.controller('mdMenu');
  this.setKeyboardMode(false);
  this.scheduleOpenMenu(menuCtrl);
};

MenuBarController.prototype.scheduleOpenMenu = function(menuCtrl) {
  var self = this;
  var $timeout = this.$timeout;
  if (menuCtrl != self.currentlyOpenMenu) {
    $timeout.cancel(self.pendingMenuOpen);
    self.pendingMenuOpen = $timeout(function() {
      self.pendingMenuOpen = undefined;
      if (self.currentlyOpenMenu) {
        self.currentlyOpenMenu.close(true, { closeAll: true });
      }
      menuCtrl.open();
    }, 200, false);
  }
};

MenuBarController.prototype.handleKeyDown = function(e) {
  var keyCodes = this.$mdConstant.KEY_CODE;
  var currentMenu = this.currentlyOpenMenu;
  var wasOpen = currentMenu && currentMenu.isOpen;
  this.setKeyboardMode(true);
  var handled, newMenu, newMenuCtrl;
  switch (e.keyCode) {
    case keyCodes.DOWN_ARROW:
      if (currentMenu) {
        currentMenu.focusMenuContainer();
      } else {
        this.openFocusedMenu();
      }
      handled = true;
      break;
    case keyCodes.UP_ARROW:
      currentMenu && currentMenu.close();
      handled = true;
      break;
    case keyCodes.LEFT_ARROW:
      newMenu = this.focusMenu(-1);
      if (wasOpen) {
        newMenuCtrl = angular.element(newMenu).controller('mdMenu');
        this.scheduleOpenMenu(newMenuCtrl);
      }
      handled = true;
      break;
    case keyCodes.RIGHT_ARROW:
      newMenu = this.focusMenu(+1);
      if (wasOpen) {
        newMenuCtrl = angular.element(newMenu).controller('mdMenu');
        this.scheduleOpenMenu(newMenuCtrl);
      }
      handled = true;
      break;
  }
  if (handled) {
    e && e.preventDefault && e.preventDefault();
    e && e.stopImmediatePropagation && e.stopImmediatePropagation();
  }
};

MenuBarController.prototype.focusMenu = function(direction) {
  var menus = this.getMenus();
  var focusedIndex = this.getFocusedMenuIndex();

  if (focusedIndex == -1) { focusedIndex = this.getOpenMenuIndex(); }

  var changed = false;

  if (focusedIndex == -1) { focusedIndex = 0; changed = true; }
  else if (
    direction < 0 && focusedIndex > 0 ||
    direction > 0 && focusedIndex < menus.length - direction
  ) {
    focusedIndex += direction;
    changed = true;
  }
  if (changed) {
    menus[focusedIndex].querySelector('button').focus();
    return menus[focusedIndex];
  }
};

MenuBarController.prototype.openFocusedMenu = function() {
  var menu = this.getFocusedMenu();
  menu && angular.element(menu).controller('mdMenu').open();
};

MenuBarController.prototype.getMenus = function() {
  var $element = this.$element;
  return this.$mdUtil.nodesToArray($element[0].children)
    .filter(function(el) { return el.nodeName == 'MD-MENU'; });
};

MenuBarController.prototype.getFocusedMenu = function() {
  return this.getMenus()[this.getFocusedMenuIndex()];
};

MenuBarController.prototype.getFocusedMenuIndex = function() {
  var $mdUtil = this.$mdUtil;
  var focusedEl = $mdUtil.getClosest(
    this.$document[0].activeElement,
    'MD-MENU'
  );
  if (!focusedEl) return -1;

  var focusedIndex = this.getMenus().indexOf(focusedEl);
  return focusedIndex;
};

MenuBarController.prototype.getOpenMenuIndex = function() {
  var menus = this.getMenus();
  for (var i = 0; i < menus.length; ++i) {
    if (menus[i].classList.contains('md-open')) return i;
  }
  return -1;
};

MenuBarController.prototype.handleParentClick = function(event) {
  var openMenu = this.querySelector('md-menu.md-open');

  if (openMenu && !openMenu.contains(event.target)) {
    angular.element(openMenu).controller('mdMenu').close(true, {
      closeAll: true
    });
  }
};

})();
(function(){
"use strict";

/**
 * @ngdoc directive
 * @name mdMenuBar
 * @module material.components.menuBar
 * @restrict E
 * @description
 *
 * Menu bars are containers that hold multiple menus. They change the behavior and appearance
 * of the `md-menu` directive to behave similar to an operating system provided menu.
 *
 * @usage
 * <hljs lang="html">
 * <md-menu-bar>
 *   <md-menu>
 *     <button ng-click="$mdMenu.open()">
 *       File
 *     </button>
 *     <md-menu-content>
 *       <md-menu-item>
 *         <md-button ng-click="ctrl.sampleAction('share', $event)">
 *           Share...
 *         </md-button>
 *       </md-menu-item>
 *       <md-menu-divider></md-menu-divider>
 *       <md-menu-item>
 *       <md-menu-item>
 *         <md-menu>
 *           <md-button ng-click="$mdMenu.open()">New</md-button>
 *           <md-menu-content>
 *             <md-menu-item><md-button ng-click="ctrl.sampleAction('New Document', $event)">Document</md-button></md-menu-item>
 *             <md-menu-item><md-button ng-click="ctrl.sampleAction('New Spreadsheet', $event)">Spreadsheet</md-button></md-menu-item>
 *             <md-menu-item><md-button ng-click="ctrl.sampleAction('New Presentation', $event)">Presentation</md-button></md-menu-item>
 *             <md-menu-item><md-button ng-click="ctrl.sampleAction('New Form', $event)">Form</md-button></md-menu-item>
 *             <md-menu-item><md-button ng-click="ctrl.sampleAction('New Drawing', $event)">Drawing</md-button></md-menu-item>
 *           </md-menu-content>
 *         </md-menu>
 *       </md-menu-item>
 *     </md-menu-content>
 *   </md-menu>
 * </md-menu-bar>
 * </hljs>
 *
 * ## Menu Bar Controls
 *
 * You may place `md-menu-item`s that function as controls within menu bars.
 * There are two modes that are exposed via the `type` attribute of the `md-menu-item`.
 * `type="checkbox"` will function as a boolean control for the `ng-model` attribute of the
 * `md-menu-item`. `type="radio"` will function like a radio button, setting the `ngModel`
 * to the `string` value of the `value` attribute. If you need non-string values, you can use
 * `ng-value` to provide an expression (this is similar to how angular's native `input[type=radio]`
 * works.
 *
 * If you want either to disable closing the opened menu when clicked, you can add the
 * `md-prevent-menu-close` attribute to the `md-menu-item`. The attribute will be forwarded to the
 * `button` element that is generated.
 *
 * <hljs lang="html">
 * <md-menu-bar>
 *  <md-menu>
 *    <button ng-click="$mdMenu.open()">
 *      Sample Menu
 *    </button>
 *    <md-menu-content>
 *      <md-menu-item type="checkbox" ng-model="settings.allowChanges" md-prevent-menu-close>
 *        Allow changes
 *      </md-menu-item>
 *      <md-menu-divider></md-menu-divider>
 *      <md-menu-item type="radio" ng-model="settings.mode" ng-value="1">Mode 1</md-menu-item>
 *      <md-menu-item type="radio" ng-model="settings.mode" ng-value="2">Mode 2</md-menu-item>
 *      <md-menu-item type="radio" ng-model="settings.mode" ng-value="3">Mode 3</md-menu-item>
 *    </md-menu-content>
 *  </md-menu>
 * </md-menu-bar>
 * </hljs>
 *
 *
 * ### Nesting Menus
 *
 * Menus may be nested within menu bars. This is commonly called cascading menus.
 * To nest a menu place the nested menu inside the content of the `md-menu-item`.
 * <hljs lang="html">
 * <md-menu-item>
 *   <md-menu>
 *     <button ng-click="$mdMenu.open()">New</md-button>
 *     <md-menu-content>
 *       <md-menu-item><md-button ng-click="ctrl.sampleAction('New Document', $event)">Document</md-button></md-menu-item>
 *       <md-menu-item><md-button ng-click="ctrl.sampleAction('New Spreadsheet', $event)">Spreadsheet</md-button></md-menu-item>
 *       <md-menu-item><md-button ng-click="ctrl.sampleAction('New Presentation', $event)">Presentation</md-button></md-menu-item>
 *       <md-menu-item><md-button ng-click="ctrl.sampleAction('New Form', $event)">Form</md-button></md-menu-item>
 *       <md-menu-item><md-button ng-click="ctrl.sampleAction('New Drawing', $event)">Drawing</md-button></md-menu-item>
 *     </md-menu-content>
 *   </md-menu>
 * </md-menu-item>
 * </hljs>
 *
 */

MenuBarDirective.$inject = ["$mdUtil", "$mdTheming"];
angular
  .module('material.components.menuBar')
  .directive('mdMenuBar', MenuBarDirective);

/* @ngInject */
function MenuBarDirective($mdUtil, $mdTheming) {
  return {
    restrict: 'E',
    require: 'mdMenuBar',
    controller: 'MenuBarController',

    compile: function compile(templateEl, templateAttrs) {
      if (!templateAttrs.ariaRole) {
        templateEl[0].setAttribute('role', 'menubar');
      }
      angular.forEach(templateEl[0].children, function(menuEl) {
        if (menuEl.nodeName == 'MD-MENU') {
          if (!menuEl.hasAttribute('md-position-mode')) {
            menuEl.setAttribute('md-position-mode', 'left bottom');

            // Since we're in the compile function and actual `md-buttons` are not compiled yet,
            // we need to query for possible `md-buttons` as well.
            menuEl.querySelector('button, a, md-button').setAttribute('role', 'menuitem');
          }
          var contentEls = $mdUtil.nodesToArray(menuEl.querySelectorAll('md-menu-content'));
          angular.forEach(contentEls, function(contentEl) {
            contentEl.classList.add('md-menu-bar-menu');
            contentEl.classList.add('md-dense');
            if (!contentEl.hasAttribute('width')) {
              contentEl.setAttribute('width', 5);
            }
          });
        }
      });

      // Mark the child menu items that they're inside a menu bar. This is necessary,
      // because mnMenuItem has special behaviour during compilation, depending on
      // whether it is inside a mdMenuBar. We can usually figure this out via the DOM,
      // however if a directive that uses documentFragment is applied to the child (e.g. ngRepeat),
      // the element won't have a parent and won't compile properly.
      templateEl.find('md-menu-item').addClass('md-in-menu-bar');

      return function postLink(scope, el, attr, ctrl) {
        el.addClass('_md');     // private md component indicator for styling
        $mdTheming(scope, el);
        ctrl.init();
      };
    }
  };

}

})();
(function(){
"use strict";


angular
  .module('material.components.menuBar')
  .directive('mdMenuDivider', MenuDividerDirective);


function MenuDividerDirective() {
  return {
    restrict: 'E',
    compile: function(templateEl, templateAttrs) {
      if (!templateAttrs.role) {
        templateEl[0].setAttribute('role', 'separator');
      }
    }
  };
}

})();
(function(){
"use strict";


MenuItemController.$inject = ["$scope", "$element", "$attrs"];
angular
  .module('material.components.menuBar')
  .controller('MenuItemController', MenuItemController);


/**
 * @ngInject
 */
function MenuItemController($scope, $element, $attrs) {
  this.$element = $element;
  this.$attrs = $attrs;
  this.$scope = $scope;
}

MenuItemController.prototype.init = function(ngModel) {
  var $element = this.$element;
  var $attrs = this.$attrs;

  this.ngModel = ngModel;
  if ($attrs.type == 'checkbox' || $attrs.type == 'radio') {
    this.mode  = $attrs.type;
    this.iconEl = $element[0].children[0];
    this.buttonEl = $element[0].children[1];
    if (ngModel) {
      // Clear ngAria set attributes
      this.initClickListeners();
    }
  }
};

// ngAria auto sets attributes on a menu-item with a ngModel.
// We don't want this because our content (buttons) get the focus
// and set their own aria attributes appropritately. Having both
// breaks NVDA / JAWS. This undeoes ngAria's attrs.
MenuItemController.prototype.clearNgAria = function() {
  var el = this.$element[0];
  var clearAttrs = ['role', 'tabindex', 'aria-invalid', 'aria-checked'];
  angular.forEach(clearAttrs, function(attr) {
    el.removeAttribute(attr);
  });
};

MenuItemController.prototype.initClickListeners = function() {
  var self = this;
  var ngModel = this.ngModel;
  var $scope = this.$scope;
  var $attrs = this.$attrs;
  var $element = this.$element;
  var mode = this.mode;

  this.handleClick = angular.bind(this, this.handleClick);

  var icon = this.iconEl;
  var button = angular.element(this.buttonEl);
  var handleClick = this.handleClick;

  $attrs.$observe('disabled', setDisabled);
  setDisabled($attrs.disabled);

  ngModel.$render = function render() {
    self.clearNgAria();
    if (isSelected()) {
      icon.style.display = '';
      button.attr('aria-checked', 'true');
    } else {
      icon.style.display = 'none';
      button.attr('aria-checked', 'false');
    }
  };

  $scope.$$postDigest(ngModel.$render);

  function isSelected() {
    if (mode == 'radio') {
      var val = $attrs.ngValue ? $scope.$eval($attrs.ngValue) : $attrs.value;
      return ngModel.$modelValue == val;
    } else {
      return ngModel.$modelValue;
    }
  }

  function setDisabled(disabled) {
    if (disabled) {
      button.off('click', handleClick);
    } else {
      button.on('click', handleClick);
    }
  }
};

MenuItemController.prototype.handleClick = function(e) {
  var mode = this.mode;
  var ngModel = this.ngModel;
  var $attrs = this.$attrs;
  var newVal;
  if (mode == 'checkbox') {
    newVal = !ngModel.$modelValue;
  } else if (mode == 'radio') {
    newVal = $attrs.ngValue ? this.$scope.$eval($attrs.ngValue) : $attrs.value;
  }
  ngModel.$setViewValue(newVal);
  ngModel.$render();
};

})();
(function(){
"use strict";


MenuItemDirective.$inject = ["$mdUtil", "$mdConstant", "$$mdSvgRegistry"];
angular
  .module('material.components.menuBar')
  .directive('mdMenuItem', MenuItemDirective);

 /* @ngInject */
function MenuItemDirective($mdUtil, $mdConstant, $$mdSvgRegistry) {
  return {
    controller: 'MenuItemController',
    require: ['mdMenuItem', '?ngModel'],
    priority: $mdConstant.BEFORE_NG_ARIA,
    compile: function(templateEl, templateAttrs) {
      var type = templateAttrs.type;
      var inMenuBarClass = 'md-in-menu-bar';

      // Note: This allows us to show the `check` icon for the md-menu-bar items.
      // The `md-in-menu-bar` class is set by the mdMenuBar directive.
      if ((type === 'checkbox' || type === 'radio') && templateEl.hasClass(inMenuBarClass)) {
        var text = templateEl[0].textContent;
        var buttonEl = angular.element('<md-button type="button"></md-button>');
        var iconTemplate = '<md-icon md-svg-src="' + $$mdSvgRegistry.mdChecked + '"></md-icon>';

        buttonEl.html(text);
        buttonEl.attr('tabindex', '0');

        if (angular.isDefined(templateAttrs.mdPreventMenuClose)) {
          buttonEl.attr('md-prevent-menu-close', templateAttrs.mdPreventMenuClose);
        }

        templateEl.html('');
        templateEl.append(angular.element(iconTemplate));
        templateEl.append(buttonEl);
        templateEl.addClass('md-indent').removeClass(inMenuBarClass);

        setDefault('role', type === 'checkbox' ? 'menuitemcheckbox' : 'menuitemradio', buttonEl);
        moveAttrToButton('ng-disabled');

      } else {
        setDefault('role', 'menuitem', templateEl[0].querySelector('md-button, button, a'));
      }


      return function(scope, el, attrs, ctrls) {
        var ctrl = ctrls[0];
        var ngModel = ctrls[1];
        ctrl.init(ngModel);
      };

      function setDefault(attr, val, el) {
        el = el || templateEl;
        if (el instanceof angular.element) {
          el = el[0];
        }
        if (!el.hasAttribute(attr)) {
          el.setAttribute(attr, val);
        }
      }

      function moveAttrToButton(attribute) {
        var attributes = $mdUtil.prefixer(attribute);

        angular.forEach(attributes, function(attr) {
          if (templateEl[0].hasAttribute(attr)) {
            var val = templateEl[0].getAttribute(attr);
            buttonEl[0].setAttribute(attr, val);
            templateEl[0].removeAttribute(attr);
          }
        });
      }
    }
  };
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.navBar
 */


MdNavBar.$inject = ["$mdAria", "$mdTheming", "$window", "$mdUtil"];
MdNavBarController.$inject = ["$element", "$scope", "$timeout", "$mdConstant"];
MdNavItem.$inject = ["$mdAria", "$$rAF", "$mdUtil", "$window"];
MdNavItemController.$inject = ["$element"];
angular.module('material.components.navBar', ['material.core'])
    .controller('MdNavBarController', MdNavBarController)
    .directive('mdNavBar', MdNavBar)
    .controller('MdNavItemController', MdNavItemController)
    .directive('mdNavItem', MdNavItem);

/**
 * @ngdoc directive
 * @name mdNavBar
 * @module material.components.navBar
 *
 * @restrict E
 *
 * @description
 * The `<md-nav-bar>` directive renders a list of material tabs that can be used
 * for top-level page navigation. Unlike `<md-tabs>`, it has no concept of a tab
 * body and no bar pagination.
 *
 * Because it deals with page navigation, certain routing concepts are built-in.
 * Route changes via `ng-href`, `ui-sref`, or `ng-click` events are supported.
 * Alternatively, the user could simply watch the value of `md-selected-nav-item`
 * (`currentNavItem` in the below example) for changes.
 *
 * Accessibility functionality is implemented as a
 * <a href="https://www.w3.org/TR/wai-aria-1.0/complete#tablist">
 *   tablist</a> with
 * <a href="https://www.w3.org/TR/wai-aria-1.0/complete#tab">tabs</a>.
 * We've kept the `role="navigation"` on the `<nav>`, for backwards compatibility, even though
 *  it is not required in the
 * <a href="https://www.w3.org/TR/wai-aria-practices/#aria_lh_navigation">
 *   latest Working Group Note from December 2017</a>.
 *
 * <h3>Keyboard Navigation</h3>
 * - `Tab`/`Shift+Tab` moves the focus to the next/previous interactive element on the page
 * - `Enter`/`Space` selects the focused nav item and navigates to display the item's contents
 * - `Right`/`Down` moves focus to the next nav item, wrapping at the end
 * - `Left`/`Up` moves focus to the previous nav item, wrapping at the end
 * - `Home`/`End` moves the focus to the first/last nav item
 *
 * @param {string=} md-selected-nav-item The name of the current tab; this must
 *     match the `name` attribute of `<md-nav-item>`.
 * @param {boolean=} md-no-ink-bar If set to true, the ink bar will be hidden.
 * @param {string=} nav-bar-aria-label An `aria-label` applied to the `md-nav-bar`'s tablist
 * for accessibility.
 *
 * @usage
 * <hljs lang="html">
 *  <md-nav-bar md-selected-nav-item="currentNavItem">
 *    <md-nav-item md-nav-click="goto('page1')" name="page1">
 *      Page One
 *    </md-nav-item>
 *    <md-nav-item md-nav-href="#page2" name="page3">Page Two</md-nav-item>
 *    <md-nav-item md-nav-sref="page3" name="page2">Page Three</md-nav-item>
 *    <md-nav-item
 *      md-nav-sref="app.page4"
 *      sref-opts="{reload: true, notify: true}"
 *      name="page4">
 *      Page Four
 *    </md-nav-item>
 *  </md-nav-bar>
 *</hljs>
 * <hljs lang="js">
 * (function() {
 *   'use strict';
 *
 *    $rootScope.$on('$routeChangeSuccess', function(event, current) {
 *      $scope.currentLink = getCurrentLinkFromRoute(current);
 *    });
 * });
 * </hljs>
 */
/**
 * @param $mdAria
 * @param $mdTheming
 * @param $window
 * @param $mdUtil
 * @constructor
 * @ngInject
 */
function MdNavBar($mdAria, $mdTheming, $window, $mdUtil) {
  return {
    restrict: 'E',
    transclude: true,
    controller: MdNavBarController,
    controllerAs: 'ctrl',
    bindToController: true,
    scope: {
      'mdSelectedNavItem': '=?',
      'mdNoInkBar': '=?',
      'navBarAriaLabel': '@?',
    },
    template:
      '<div class="md-nav-bar">' +
        '<nav role="navigation">' +
          '<ul class="_md-nav-bar-list" ng-transclude role="tablist" ' +
            'ng-focus="ctrl.onFocus()" ' + // Deprecated but kept for now in order to not break tests
            'aria-label="{{ctrl.navBarAriaLabel}}">' +
          '</ul>' +
        '</nav>' +
        '<md-nav-ink-bar ng-hide="ctrl.mdNoInkBar"></md-nav-ink-bar>' +
      '</div>',
    link: function(scope, element, attrs, ctrl) {

      ctrl.width = $window.innerWidth;

      function onResize() {
        if (ctrl.width !== $window.innerWidth) {
          ctrl.updateSelectedTabInkBar();
          ctrl.width = $window.innerWidth;
          scope.$digest();
        }
      }

      function cleanUp() {
        angular.element($window).off('resize', onResize);
      }

      angular.element($window).on('resize', $mdUtil.debounce(onResize, 300));
      scope.$on('$destroy', cleanUp);

      $mdTheming(element);
      if (!ctrl.navBarAriaLabel) {
        $mdAria.expectAsync(element, 'aria-label', angular.noop);
      }
    },
  };
}

/**
 * Controller for the nav-bar component.
 * Accessibility functionality is implemented as a tablist
 * (https://www.w3.org/TR/wai-aria-1.0/complete#tablist) and
 * tabs (https://www.w3.org/TR/wai-aria-1.0/complete#tab).
 *
 * @param {!angular.JQLite} $element
 * @param {!angular.Scope} $scope
 * @param {!angular.Timeout} $timeout
 * @param {!Object} $mdConstant
 * @constructor
 * @final
 * @ngInject
 */
function MdNavBarController($element, $scope, $timeout, $mdConstant) {
  // Injected variables
  /** @private @const {!angular.Timeout} */
  this._$timeout = $timeout;

  /** @private @const {!angular.Scope} */
  this._$scope = $scope;

  /** @private @const {!Object} */
  this._$mdConstant = $mdConstant;

  // Data-bound variables.
  /** @type {string} */
  this.mdSelectedNavItem;

  /** @type {string} */
  this.navBarAriaLabel;

  // State variables.

  /** @type {?angular.JQLite} */
  this._navBarEl = $element[0];

  /** @type {?angular.JQLite} */
  this._inkbar;

  var self = this;
  // need to wait for transcluded content to be available
  var deregisterTabWatch = this._$scope.$watch(function() {
    return self._navBarEl.querySelectorAll('._md-nav-button').length;
  },
  function(newLength) {
    if (newLength > 0) {
      self._initTabs();
      deregisterTabWatch();
    }
  });
}

/**
 * Initializes the tab components once they exist.
 * @private
 */
MdNavBarController.prototype._initTabs = function() {
  this._inkbar = angular.element(this._navBarEl.querySelector('md-nav-ink-bar'));

  var self = this;
  this._$timeout(function() {
    self._updateTabs(self.mdSelectedNavItem, null);
  });

  this._$scope.$watch('ctrl.mdSelectedNavItem', function(newValue, oldValue) {
    // Wait a digest before update tabs for products doing
    // anything dynamic in the template.
    self._$timeout(function() {
      self._updateTabs(newValue, oldValue);
    });
  });
};

/**
 * Set the current tab to be selected.
 * @param {string|undefined} newValue New current tab name.
 * @param {string|undefined|null} oldValue Previous tab name.
 * @private
 */
MdNavBarController.prototype._updateTabs = function(newValue, oldValue) {
  var self = this;
  var tabs = this._getTabs();
  var sameTab = newValue === oldValue;

  // this._getTabs can return null if nav-bar has not yet been initialized
  if (!tabs) return;

  var oldIndex = -1;
  var newIndex = -1;
  var newTab = this._getTabByName(newValue);
  var oldTab = this._getTabByName(oldValue);

  if (oldTab) {
    oldTab.setSelected(false);
    oldIndex = tabs.indexOf(oldTab);
  }

  if (newTab) {
    newTab.setSelected(true);
    newIndex = tabs.indexOf(newTab);
  }

  this._$timeout(function() {
    self._updateInkBarStyles(newTab, newIndex, oldIndex);
    // Don't change focus when there is no newTab, the new and old tabs are the same, or when
    // called from MdNavBarController._initTabs() which would have no oldTab defined.
    if (newTab && oldTab && !sameTab) {
      self._moveFocus(oldTab, newTab);
    }
  });
};

/**
 * Repositions the ink bar to the selected tab.
 * @private
 */
MdNavBarController.prototype._updateInkBarStyles = function(tab, newIndex) {
  this._inkbar.css({display: newIndex < 0 ? 'none' : ''});

  if (tab) {
    var tabEl = tab.getButtonEl();
    var left = tabEl.offsetLeft;
    var tabWidth = tabEl.offsetWidth;
    var navBarWidth = this._navBarEl.getBoundingClientRect().width;
    var scale = tabWidth / navBarWidth;
    var translate = left / navBarWidth * 100;

    this._inkbar.css({ transform: 'translateX(' + translate + '%) scaleX(' + scale + ')' });
  }
};

/**
 * Updates inkbar to match current tab.
 */
MdNavBarController.prototype.updateSelectedTabInkBar = function() {
  this._updateInkBarStyles(this._getSelectedTab());
}

/**
 * Returns an array of the current tabs.
 * @return {Array<!MdNavItemController>}
 * @private
 */
MdNavBarController.prototype._getTabs = function() {
  var controllers = Array.prototype.slice.call(
    this._navBarEl.querySelectorAll('.md-nav-item'))
    .map(function(el) {
      return angular.element(el).controller('mdNavItem');
    });
  return controllers.indexOf(undefined) ? controllers : null;
};

/**
 * Returns the tab with the specified name.
 * @param {string} name The name of the tab, found in its name attribute.
 * @return {MdNavItemController}
 * @private
 */
MdNavBarController.prototype._getTabByName = function(name) {
  return this._findTab(function(tab) {
    return tab.getName() === name;
  });
};

/**
 * Returns the selected tab.
 * @return {MdNavItemController}
 * @private
 */
MdNavBarController.prototype._getSelectedTab = function() {
  return this._findTab(function(tab) {
    return tab.isSelected();
  });
};

/**
 * Returns the focused tab.
 * @return {MdNavItemController}
 */
MdNavBarController.prototype.getFocusedTab = function() {
  return this._findTab(function(tab) {
    return tab.hasFocus();
  });
};

/**
 * Find a tab that matches the specified function, starting from the first tab.
 * @param {Function} fn
 * @param {number=} startIndex index to start at. Defaults to 0.
 * @returns {MdNavItemController}
 * @private
 */
MdNavBarController.prototype._findTab = function(fn, startIndex) {
  var tabs = this._getTabs();
  if (startIndex === undefined || startIndex === null) {
    startIndex = 0;
  }
  for (var i = startIndex; i < tabs.length; i++) {
    if (fn(tabs[i])) {
      return tabs[i];
    }
  }
  return null;
};

/**
 * Find a tab that matches the specified function, going backwards from the end of the list.
 * @param {Function} fn
 * @param {number=} startIndex index to start at. Defaults to tabs.length - 1.
 * @returns {MdNavItemController}
 * @private
 */
MdNavBarController.prototype._findTabReverse = function(fn, startIndex) {
  var tabs = this._getTabs();
  if (startIndex === undefined || startIndex === null) {
    startIndex = tabs.length - 1;
  }
  for (var i = startIndex; i >= 0 ; i--) {
    if (fn(tabs[i])) {
      return tabs[i];
    }
  }
  return null;
};

/**
 * Direct focus to the selected tab when focus enters the nav bar.
 */
MdNavBarController.prototype.onFocus = function() {
  var tab = this._getSelectedTab();
  if (tab && !tab._focused) {
    tab.setFocused(true);
  }
};

/**
 * Move focus from oldTab to newTab.
 * @param {!MdNavItemController} oldTab
 * @param {!MdNavItemController} newTab
 * @private
 */
MdNavBarController.prototype._moveFocus = function(oldTab, newTab) {
  oldTab.setFocused(false);
  newTab.setFocused(true);
};

/**
 * Focus the first tab.
 * @private
 */
MdNavBarController.prototype._focusFirstTab = function() {
  var tabs = this._getTabs();
  if (!tabs) return;
  var tabToFocus = this._findTab(function(tab) {
    return tab._isEnabled();
  });
  if (tabToFocus) {
    this._moveFocus(this.getFocusedTab(), tabToFocus);
  }
};

/**
 * Focus the last tab.
 * @private
 */
MdNavBarController.prototype._focusLastTab = function() {
  var tabs = this._getTabs();
  if (!tabs) return;
  var tabToFocus = this._findTabReverse(function(tab) {
    return tab._isEnabled();
  });
  if (tabToFocus) {
    this._moveFocus(this.getFocusedTab(), tabToFocus);
  }
};

/**
 * Focus the next non-disabled tab.
 * @param {number} focusedTabIndex the index of the currently focused tab
 * @private
 */
MdNavBarController.prototype._focusNextTab = function(focusedTabIndex) {
  var tabs = this._getTabs();
  if (!tabs) return;
  var tabToFocus = this._findTab(function(tab) {
    return tab._isEnabled();
  }, focusedTabIndex + 1);
  if (tabToFocus) {
    this._moveFocus(this.getFocusedTab(), tabToFocus);
  } else {
    this._focusFirstTab();
  }
};

/**
 * Focus the previous non-disabled tab.
 * @param {number} focusedTabIndex the index of the currently focused tab
 * @private
 */
MdNavBarController.prototype._focusPreviousTab = function(focusedTabIndex) {
  var tabs = this._getTabs();
  if (!tabs) return;
  var tabToFocus = this._findTabReverse(function(tab) {
    return tab._isEnabled();
  }, focusedTabIndex - 1);
  if (tabToFocus) {
    this._moveFocus(this.getFocusedTab(), tabToFocus);
  } else {
    this._focusLastTab();
  }
};

/**
 * Responds to keydown events.
 * Calls to preventDefault() stop the page from scrolling when changing focus in the nav-bar.
 * @param {!KeyboardEvent} e
 */
MdNavBarController.prototype.onKeydown = function(e) {
  var keyCodes = this._$mdConstant.KEY_CODE;
  var tabs = this._getTabs();
  var focusedTab = this.getFocusedTab();
  if (!focusedTab || !tabs) return;

  var focusedTabIndex = tabs.indexOf(focusedTab);

  // use arrow keys to navigate between tabs
  switch (e.keyCode) {
    case keyCodes.UP_ARROW:
    case keyCodes.LEFT_ARROW:
      e.preventDefault();
      this._focusPreviousTab(focusedTabIndex);
      break;
    case keyCodes.DOWN_ARROW:
    case keyCodes.RIGHT_ARROW:
      e.preventDefault();
      this._focusNextTab(focusedTabIndex);
      break;
    case keyCodes.SPACE:
    case keyCodes.ENTER:
      // timeout to avoid a "digest already in progress" console error
      this._$timeout(function() {
        focusedTab.getButtonEl().click();
      });
      break;
    case keyCodes.HOME:
      e.preventDefault();
      this._focusFirstTab();
      break;
    case keyCodes.END:
      e.preventDefault();
      this._focusLastTab();
      break;
  }
};

/**
 * @ngdoc directive
 * @name mdNavItem
 * @module material.components.navBar
 *
 * @restrict E
 *
 * @description
 * `<md-nav-item>` describes a page navigation link within the `<md-nav-bar>` component.
 * It renders an `<md-button>` as the actual link.
 *
 * Exactly one of the `md-nav-click`, `md-nav-href`, or `md-nav-sref` attributes are required
 * to be specified.
 *
 * @param {string=} nav-item-aria-label Allows setting or overriding the label that is announced by
 *     a screen reader for the nav item's button. If this is not set, the nav item's transcluded
 *     content will be announced. Make sure to set this if the nav item's transcluded content does
 *     not include descriptive text, for example only an icon.
 * @param {expression=} md-nav-click Expression which will be evaluated when the
 *     link is clicked to change the page. Renders as an `ng-click`.
 * @param {string=} md-nav-href url to transition to when this link is clicked.
 *     Renders as an `ng-href`.
 * @param {string=} md-nav-sref UI-Router state to transition to when this link is
 *     clicked. Renders as a `ui-sref`.
 * @param {string} name The name of this link. Used by the nav bar to know
 *     which link is currently selected.
 * @param {!object=} sref-opts UI-Router options that are passed to the `$state.go()` function. See
 *     the <a ng-href="https://ui-router.github.io/docs/latest/interfaces/transition.transitionoptions.html"
 *     target="_blank">UI-Router documentation for details</a>.
 *
 * @usage
 * See <a ng-href="api/directive/mdNavBar">md-nav-bar</a> for usage.
 */
/**
 * @param $mdAria
 * @param $$rAF
 * @param $mdUtil
 * @param $window
 * @constructor
 * @ngInject
 */
function MdNavItem($mdAria, $$rAF, $mdUtil, $window) {
  return {
    restrict: 'E',
    require: ['mdNavItem', '^mdNavBar'],
    controller: MdNavItemController,
    bindToController: true,
    controllerAs: 'ctrl',
    replace: true,
    transclude: true,
    template: function(tElement, tAttrs) {
      var hasNavClick = tAttrs.mdNavClick;
      var hasNavHref = tAttrs.mdNavHref;
      var hasNavSref = tAttrs.mdNavSref;
      var hasSrefOpts = tAttrs.srefOpts;
      var navigationAttribute;
      var navigationOptions;
      var buttonTemplate;

      // Cannot specify more than one nav attribute
      if ((hasNavClick ? 1 : 0) + (hasNavHref ? 1 : 0) + (hasNavSref ? 1 : 0) > 1) {
        throw Error(
          'Please do not specify more than one of the md-nav-click, md-nav-href, ' +
          'or md-nav-sref attributes per nav-item directive.'
        );
      }

      if (hasNavClick !== undefined && hasNavClick !== null) {
        navigationAttribute = 'ng-click="ctrl.mdNavClick()"';
      } else if (hasNavHref !== undefined && hasNavHref !== null) {
        navigationAttribute = 'ng-href="{{ctrl.mdNavHref}}"';
      } else if (hasNavSref !== undefined && hasNavSref !== null) {
        navigationAttribute = 'ui-sref="{{ctrl.mdNavSref}}"';
      } else {
        throw Error(
          'Please specify at least one of the md-nav-click, md-nav-href, or md-nav-sref ' +
          'attributes per nav-item directive.');
      }

      navigationOptions = hasSrefOpts ? 'ui-sref-opts="{{ctrl.srefOpts}}" ' : '';

      if (navigationAttribute) {
        buttonTemplate = '' +
          '<md-button class="_md-nav-button md-accent" ' +
            'ng-class="ctrl.getNgClassMap()" ' +
            'ng-blur="ctrl.setFocused(false)" ' +
            'ng-disabled="ctrl.disabled" ' +
            'tabindex="-1" ' +
            'role="tab" ' +
            'ng-attr-aria-label="{{ctrl.navItemAriaLabel ? ctrl.navItemAriaLabel : undefined}}" ' +
            'aria-selected="{{ctrl.isSelected()}}" ' +
            navigationOptions +
            navigationAttribute + '>' +
            '<span ng-transclude class="_md-nav-button-text"></span>' +
          '</md-button>';
      }

      return '' +
        '<li class="md-nav-item" ' +
          'role="presentation">' +
          (buttonTemplate || '') +
        '</li>';
    },
    scope: {
      'mdNavClick': '&?',
      'mdNavHref': '@?',
      'mdNavSref': '@?',
      'srefOpts': '=?',
      'name': '@',
      'navItemAriaLabel': '@?',
    },
    link: function(scope, element, attrs, controllers) {
      var disconnect;
      var mdNavItem;
      var mdNavBar;
      var navButton;

      // When accessing the element's contents synchronously, they
      // may not be defined yet because of transclusion. There is a higher
      // chance that it will be accessible if we wait one frame.
      $$rAF(function() {
        mdNavItem = controllers[0];
        mdNavBar = controllers[1];
        navButton = angular.element(element[0].querySelector('._md-nav-button'));

        if (!mdNavItem.name) {
          mdNavItem.name = angular.element(element[0]
              .querySelector('._md-nav-button-text')).text().trim();
        }

        navButton.on('keydown', function($event) {
          mdNavBar.onKeydown($event);
        });

        navButton.on('focus', function() {
          mdNavItem._focused = true;
        });

        navButton.on('click', function() {
          // This triggers a watcher on mdNavBar.mdSelectedNavItem which calls
          // MdNavBarController._updateTabs() after a $timeout. That function calls
          // MdNavItemController.setSelected() for the old tab with false and the new tab with true.
          mdNavBar.mdSelectedNavItem = mdNavItem.name;
          scope.$apply();
        });

        // Get the disabled attribute value first, then setup observing of value changes
        mdNavItem.disabled = $mdUtil.parseAttributeBoolean(attrs['disabled'], false);
        if ('MutationObserver' in $window) {
          var config = {attributes: true, attributeFilter: ['disabled']};
          var targetNode = element[0];
          var mutationCallback = function(mutationList) {
            $mdUtil.nextTick(function() {
              mdNavItem.disabled = $mdUtil.parseAttributeBoolean(attrs[mutationList[0].attributeName], false);
            });
          };
          var observer = new MutationObserver(mutationCallback);
          observer.observe(targetNode, config);
          disconnect = observer.disconnect.bind(observer);
        } else {
          attrs.$observe('disabled', function (value) {
            mdNavItem.disabled = $mdUtil.parseAttributeBoolean(value, false);
          });
        }

        if (!mdNavItem.navItemAriaLabel) {
          $mdAria.expectWithText(navButton, 'aria-label');
        }
      });

      scope.$on('destroy', function() {
        navButton.off('keydown');
        navButton.off('focus');
        navButton.off('click');
        disconnect();
      });
    }
  };
}

/**
 * Controller for the nav-item component.
 * @param {!angular.JQLite} $element
 * @constructor
 * @final
 * @ngInject
 */
function MdNavItemController($element) {

  /** @private @const {!angular.JQLite} */
  this._$element = $element;

  // Data-bound variables

  /** @const {?Function} */
  this.mdNavClick;

  /** @const {?string} */
  this.mdNavHref;

  /** @const {?string} */
  this.mdNavSref;
  /** @const {?Object} */
  this.srefOpts;
  /** @const {?string} */
  this.name;

  /** @type {string} */
  this.navItemAriaLabel;

  // State variables
  /** @private {boolean} */
  this._selected = false;

  /** @private {boolean} */
  this._focused = false;
}

/**
 * Returns a map of class names and values for use by ng-class.
 * @return {!Object<string,boolean>}
 */
MdNavItemController.prototype.getNgClassMap = function() {
  return {
    'md-active': this._selected,
    'md-primary': this._selected,
    'md-unselected': !this._selected,
    'md-focused': this._focused,
  };
};

/**
 * Get the name attribute of the tab.
 * @return {string}
 */
MdNavItemController.prototype.getName = function() {
  return this.name;
};

/**
 * Get the button element associated with the tab.
 * @return {!Element}
 */
MdNavItemController.prototype.getButtonEl = function() {
  return this._$element[0].querySelector('._md-nav-button');
};

/**
 * Set the selected state of the tab and updates the tabindex.
 * This function is called for the oldTab and newTab when selection changes.
 * @param {boolean} isSelected true to select the tab, false to deselect the tab
 */
MdNavItemController.prototype.setSelected = function(isSelected) {
  this._selected = isSelected;
  if (isSelected) {
    // https://www.w3.org/TR/wai-aria-practices/examples/tabs/tabs-2/tabs.html suggests that we call
    // removeAttribute('tabindex') here, but that causes our unit tests to fail due to
    // document.activeElement staying set to the body instead of the focused nav button.
    this.getButtonEl().setAttribute('tabindex', '0');
  } else {
    this.getButtonEl().setAttribute('tabindex', '-1');
  }
};

/**
 * @return {boolean}
 */
MdNavItemController.prototype.isSelected = function() {
  return this._selected;
};

/**
 * Set the focused state of the tab.
 * @param {boolean} isFocused
 */
MdNavItemController.prototype.setFocused = function(isFocused) {
  this._focused = isFocused;

  if (isFocused) {
    this.getButtonEl().focus();
  }
};

/**
 * @return {boolean} true if the tab has focus, false if not.
 */
MdNavItemController.prototype.hasFocus = function() {
  return this._focused;
};

/**
 * @return {boolean} true if the tab is enabled, false if disabled.
 * @private
 */
MdNavItemController.prototype._isEnabled = function() {
  return !this._$element.attr('disabled');
};

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.panel
 */
MdPanelService.$inject = ["presets", "$rootElement", "$rootScope", "$injector", "$window"];
angular
  .module('material.components.panel', [
    'material.core',
    'material.components.backdrop'
  ])
  .provider('$mdPanel', MdPanelProvider);


/*****************************************************************************
 *                            PUBLIC DOCUMENTATION                           *
 *****************************************************************************/


/**
 * @ngdoc service
 * @name $mdPanelProvider
 * @module material.components.panel
 *
 * @description
 * `$mdPanelProvider` allows users to create configuration presets that will be
 * stored within a cached presets object. When the configuration is needed, the
 * user can request the preset by passing it as the first parameter in the
 * `$mdPanel.create` or `$mdPanel.open` methods.
 *
 * @usage
 * <hljs lang="js">
 * (function(angular, undefined) {
 *   'use strict';
 *
 *   angular
 *       .module('demoApp', ['ngMaterial'])
 *       .config(DemoConfig)
 *       .controller('DemoCtrl', DemoCtrl)
 *       .controller('DemoMenuCtrl', DemoMenuCtrl);
 *
 *   function DemoConfig($mdPanelProvider) {
 *     $mdPanelProvider.definePreset('demoPreset', {
 *       attachTo: angular.element(document.body),
 *       controller: DemoMenuCtrl,
 *       controllerAs: 'ctrl',
 *       template: '' +
 *           '<div class="menu-panel" md-whiteframe="4">' +
 *           '  <div class="menu-content">' +
 *           '    <div class="menu-item" ng-repeat="item in ctrl.items">' +
 *           '      <button class="md-button">' +
 *           '        <span>{{item}}</span>' +
 *           '      </button>' +
 *           '    </div>' +
 *           '    <md-divider></md-divider>' +
 *           '    <div class="menu-item">' +
 *           '      <button class="md-button" ng-click="ctrl.closeMenu()">' +
 *           '        <span>Close Menu</span>' +
 *           '      </button>' +
 *           '    </div>' +
 *           '  </div>' +
 *           '</div>',
 *       panelClass: 'menu-panel-container',
 *       focusOnOpen: false,
 *       zIndex: 100,
 *       propagateContainerEvents: true,
 *       groupName: 'menus'
 *     });
 *   }
 *
 *   function PanelProviderCtrl($mdPanel) {
 *     this.navigation = {
 *       name: 'navigation',
 *       items: [
 *         'Home',
 *         'About',
 *         'Contact'
 *       ]
 *     };
 *     this.favorites = {
 *       name: 'favorites',
 *       items: [
 *         'Add to Favorites'
 *       ]
 *     };
 *     this.more = {
 *       name: 'more',
 *       items: [
 *         'Account',
 *         'Sign Out'
 *       ]
 *     };
 *
 *     $mdPanel.newPanelGroup('menus', {
 *       maxOpen: 2
 *     });
 *
 *     this.showMenu = function($event, menu) {
 *       $mdPanel.open('demoPreset', {
 *         id: 'menu_' + menu.name,
 *         position: $mdPanel.newPanelPosition()
 *             .relativeTo($event.target)
 *             .addPanelPosition(
 *               $mdPanel.xPosition.ALIGN_START,
 *               $mdPanel.yPosition.BELOW
 *             ),
 *         locals: {
 *           items: menu.items
 *         },
 *         openFrom: $event
 *       });
 *     };
 *   }
 *
 *   function PanelMenuCtrl(mdPanelRef) {
 *     // 'mdPanelRef' is injected in the controller.
 *     this.closeMenu = function() {
 *       if (mdPanelRef) {
 *         mdPanelRef.close();
 *       }
 *     };
 *   }
 * })(angular);
 * </hljs>
 */

/**
 * @ngdoc method
 * @name $mdPanelProvider#definePreset
 * @description
 * Takes the passed in preset name and preset configuration object and adds it
 * to the `_presets` object of the provider. This `_presets` object is then
 * passed along to the `$mdPanel` service.
 *
 * @param {string} name Preset name.
 * @param {!Object} preset Specific configuration object that can contain any
 *     and all of the parameters available within the `$mdPanel.create` method.
 *     However, parameters that pertain to id, position, animation, and user
 *     interaction are not allowed and will be removed from the preset
 *     configuration.
 */


/*****************************************************************************
 *                               MdPanel Service                             *
 *****************************************************************************/


/**
 * @ngdoc service
 * @name $mdPanel
 * @module material.components.panel
 *
 * @description
 * `$mdPanel` is a robust, low-level service for creating floating panels on
 * the screen. It can be used to implement tooltips, dialogs, pop-ups, etc.
 *
 * The following types, referenced below, have separate documentation:
 * - <a ng-href="api/type/MdPanelAnimation">MdPanelAnimation</a> from `$mdPanel.newPanelAnimation()`
 * - <a ng-href="api/type/MdPanelPosition">MdPanelPosition</a> from `$mdPanel.newPanelPosition()`
 * - <a ng-href="api/type/MdPanelRef">MdPanelRef</a> from the `$mdPanel.open()` Promise or
 * injected in the panel's controller
 *
 * @usage
 * <hljs lang="js">
 * (function(angular, undefined) {
 *   'use strict';
 *
 *   angular
 *       .module('demoApp', ['ngMaterial'])
 *       .controller('DemoDialogController', DialogController)
 *       .controller('DemoCtrl', function($mdPanel) {
 *
 *     var panelRef;
 *
 *     function showPanel($event) {
 *       var panelPosition = $mdPanel.newPanelPosition()
 *           .absolute()
 *           .top('50%')
 *           .left('50%');
 *
 *       var panelAnimation = $mdPanel.newPanelAnimation()
 *           .openFrom($event)
 *           .duration(200)
 *           .closeTo('.show-button')
 *           .withAnimation($mdPanel.animation.SCALE);
 *
 *       var config = {
 *         attachTo: angular.element(document.body),
 *         controller: DialogController,
 *         controllerAs: 'ctrl',
 *         position: panelPosition,
 *         animation: panelAnimation,
 *         targetEvent: $event,
 *         templateUrl: 'dialog-template.html',
 *         clickOutsideToClose: true,
 *         escapeToClose: true,
 *         focusOnOpen: true
 *       };
 *
 *       $mdPanel.open(config)
 *           .then(function(result) {
 *             panelRef = result;
 *           });
 *     }
 *   }
 *
 *   function DialogController(MdPanelRef) {
 *     function closeDialog() {
 *       if (MdPanelRef) MdPanelRef.close();
 *     }
 *   }
 * })(angular);
 * </hljs>
 */

/**
 * @ngdoc method
 * @name $mdPanel#create
 * @description
 * Creates a panel with the specified options.
 *
 * @param config {!Object=} Specific configuration object that may contain the
 *     following properties:
 *
 *   - `id` - `{string=}`: An ID to track the panel by. When an ID is provided,
 *     the created panel is added to a tracked panels object. Any subsequent
 *     requests made to create a panel with that ID are ignored. This is useful
 *     in having the panel service not open multiple panels from the same user
 *     interaction when there is no backdrop and events are propagated. Defaults
 *     to an arbitrary string that is not tracked.
 *   - `template` - `{string=}`: HTML template to show in the panel. This
 *     **must** be trusted HTML with respect to AngularJS’s
 *     [$sce service](https://docs.angularjs.org/api/ng/service/$sce).
 *   - `templateUrl` - `{string=}`: The URL that will be used as the content of
 *     the panel.
 *   - `contentElement` - `{(string|!JQLite|!Element)=}`: Pre-compiled
 *     element to be used as the panel's content.
 *   - `controller` - `{(function|string)=}`: The controller to associate with
 *     the panel. The controller can inject a reference to the returned
 *     panelRef, which allows the panel to be closed, hidden, and shown. Any
 *     fields passed in through locals or resolve will be bound to the
 *     controller.
 *   - `controllerAs` - `{string=}`: An alias to assign the controller to on
 *     the scope.
 *   - `bindToController` - `{boolean=}`: Binds locals to the controller
 *     instead of passing them in. Defaults to true, as this is a best
 *     practice.
 *   - `locals` - `{Object=}`: An object containing key/value pairs. The keys
 *     will be used as names of values to inject into the controller. For
 *     example, `locals: {three: 3}` would inject `three` into the controller,
 *     with the value 3. 'mdPanelRef' is a reserved key, and will always
 *     be set to the created MdPanelRef instance.
 *   - `resolve` - `{Object=}`: Similar to locals, except it takes promises as
 *     values. The panel will not open until all of the promises resolve.
 *   - `attachTo` - `{(string|!JQLite|!Element)=}`: The element to
 *     attach the panel to. Defaults to appending to the root element of the
 *     application.
 *   - `propagateContainerEvents` - `{boolean=}`: Whether pointer or touch
 *     events should be allowed to propagate 'go through' the container, aka the
 *     wrapper, of the panel. Defaults to false.
 *   - `panelClass` - `{string=}`: A css class to apply to the panel element.
 *     This class should define any borders, box-shadow, etc. for the panel.
 *   - `zIndex` - `{number=}`: The z-index to place the panel at.
 *     Defaults to 80.
 *   - `position` - `{MdPanelPosition=}`: An MdPanelPosition object that
 *     specifies the alignment of the panel. For more information, see
 *     `MdPanelPosition`.
 *   - `clickOutsideToClose` - `{boolean=}`: Whether the user can click
 *     outside the panel to close it. Defaults to false.
 *   - `escapeToClose` - `{boolean=}`: Whether the user can press escape to
 *     close the panel. Defaults to false.
 *   - `onCloseSuccess` - `{function(!panelRef, string)=}`: Function that is
 *     called after the close successfully finishes. The first parameter passed
 *     into this function is the current panelRef and the 2nd is an optional
 *     string explaining the close reason. The currently supported closeReasons
 *     can be found in the MdPanelRef.closeReasons enum. These are by default
 *     passed along by the panel.
 *   - `trapFocus` - `{boolean=}`: Whether focus should be trapped within the
 *     panel. If `trapFocus` is true, the user will not be able to interact
 *     with the rest of the page until the panel is dismissed. Defaults to
 *     false.
 *   - `focusOnOpen` - `{boolean=}`: An option to override focus behavior on
 *     open. Only disable if focusing some other way, as focus management is
 *     required for panels to be accessible. Defaults to true.
 *   - `fullscreen` - `{boolean=}`: Whether the panel should be full screen.
 *     Applies the class `._md-panel-fullscreen` to the panel on open. Defaults
 *     to false.
 *   - `animation` - `{MdPanelAnimation=}`: An MdPanelAnimation object that
 *     specifies the animation of the panel. For more information, see
 *     `MdPanelAnimation`.
 *   - `hasBackdrop` - `{boolean=}`: Whether there should be an opaque backdrop
 *     behind the panel. Defaults to false.
 *   - `disableParentScroll` - `{boolean=}`: Whether the user can scroll the
 *     page behind the panel. Defaults to false.
 *   - `onDomAdded` - `{function=}`: Callback function used to announce when
 *     the panel is added to the DOM.
 *   - `onOpenComplete` - `{function=}`: Callback function used to announce
 *     when the open() action is finished.
 *   - `onRemoving` - `{function=}`: Callback function used to announce the
 *     close/hide() action is starting.
 *   - `onDomRemoved` - `{function=}`: Callback function used to announce when
 *     the panel is removed from the DOM.
 *   - `origin` - `{(string|!JQLite|!Element)=}`: The element to focus
 *     on when the panel closes. This is commonly the element which triggered
 *     the opening of the panel. If you do not use `origin`, you need to control
 *     the focus manually.
 *   - `groupName` - `{(string|!Array<string>)=}`: A group name or an array of
 *     group names. The group name is used for creating a group of panels. The
 *     group is used for configuring the number of open panels and identifying
 *     specific behaviors for groups. For instance, all tooltips could be
 *     identified using the same groupName.
 *
 * @returns {!MdPanelRef} panelRef
 */

/**
 * @ngdoc method
 * @name $mdPanel#open
 * @description
 * Calls the create method above, then opens the panel. This is a shortcut for
 * creating and then calling open manually. If custom methods need to be
 * called when the panel is added to the DOM or opened, do not use this method.
 * Instead create the panel, chain promises on the domAdded and openComplete
 * methods, and call open from the returned panelRef.
 *
 * @param {!Object=} config Specific configuration object that may contain
 *     the properties defined in `$mdPanel.create`.
 * @returns {!Q.IPromise<!MdPanelRef>} panelRef A promise that resolves
 *     to an instance of the panel.
 */

/**
 * @ngdoc method
 * @name $mdPanel#newPanelPosition
 * @description
 * Returns a new instance of the MdPanelPosition object. Use this to create
 * the position config object.
 *
 * @returns {!MdPanelPosition} panelPosition
 */

/**
 * @ngdoc method
 * @name $mdPanel#newPanelAnimation
 * @description
 * Returns a new instance of the MdPanelAnimation object. Use this to create
 * the animation config object.
 *
 * @returns {!MdPanelAnimation} panelAnimation
 */

/**
 * @ngdoc method
 * @name $mdPanel#newPanelGroup
 * @description
 * Creates a panel group and adds it to a tracked list of panel groups.
 *
 * @param {string} groupName Name of the group to create.
 * @param {!Object=} config Specific configuration object that may contain the
 *     following properties:
 *
 *   - `maxOpen` - `{number=}`: The maximum number of panels that are allowed to
 *     be open within a defined panel group.
 *
 * @returns {!Object<string,
 *     {panels: !Array<!MdPanelRef>,
 *     openPanels: !Array<!MdPanelRef>,
 *     maxOpen: number}>} panelGroup
 */

/**
 * @ngdoc method
 * @name $mdPanel#setGroupMaxOpen
 * @description
 * Sets the maximum number of panels in a group that can be opened at a given
 * time.
 *
 * @param {string} groupName The name of the group to configure.
 * @param {number} maxOpen The maximum number of panels that can be
 *     opened. Infinity can be passed in to remove the maxOpen limit.
 */


/*****************************************************************************
 *                                 MdPanelRef                                *
 *****************************************************************************/


/**
 * @ngdoc type
 * @name MdPanelRef
 * @module material.components.panel
 * @description
 * A reference to a created panel. This reference contains a unique id for the
 * panel, along with the following properties:
 *
 *   - `id` - `{string}`: The unique id for the panel. This id is used to track
 *     when a panel was interacted with.
 *   - `config` - `{!Object=}`: The entire config object that was used in
 *     create.
 *   - `isAttached` - `{boolean}`: Whether the panel is attached to the DOM.
 *     Visibility to the user does not factor into isAttached.
 *   - `panelContainer` - `{JQLite}`: The wrapper element containing the
 *     panel. This property is added in order to have access to the `addClass`,
 *     `removeClass`, `toggleClass`, etc methods.
 *   - `panelEl` - `{JQLite}`: The panel element. This property is added
 *     in order to have access to the `addClass`, `removeClass`, `toggleClass`,
 *     etc methods.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#open
 * @description
 * Attaches and shows the panel.
 *
 * @returns {!Q.IPromise} A promise that is resolved when the panel is
 *     opened.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#close
 * @description
 * Hides and detaches the panel. Note that this will **not** destroy the panel.
 * If you don't intend on using the panel again, call the {@link #destroy
 * destroy} method afterwards.
 *
 * @returns {!Q.IPromise} A promise that is resolved when the panel is
 *     closed.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#attach
 * @description
 * Create the panel elements and attach them to the DOM. The panel will be
 * hidden by default.
 *
 * @returns {!Q.IPromise} A promise that is resolved when the panel is
 *     attached.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#detach
 * @description
 * Removes the panel from the DOM. This will NOT hide the panel before removing
 * it.
 *
 * @returns {!Q.IPromise} A promise that is resolved when the panel is
 *     detached.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#show
 * @description
 * Shows the panel.
 *
 * @returns {!Q.IPromise} A promise that is resolved when the panel has
 *     shown and animations are completed.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#hide
 * @description
 * Hides the panel.
 *
 * @returns {!Q.IPromise} A promise that is resolved when the panel has
 *     hidden and animations are completed.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#destroy
 * @description
 * Destroys the panel. The panel cannot be opened again after this is called.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#addClass
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 * @description
 * Adds a class to the panel. DO NOT use this hide/show the panel.
 *
 * @param {string} newClass class to be added.
 * @param {boolean} toElement Whether or not to add the class to the panel
 *     element instead of the container.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#removeClass
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 * @description
 * Removes a class from the panel. DO NOT use this to hide/show the panel.
 *
 * @param {string} oldClass Class to be removed.
 * @param {boolean} fromElement Whether or not to remove the class from the
 *     panel element instead of the container.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#toggleClass
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 * @description
 * Toggles a class on the panel. DO NOT use this to hide/show the panel.
 *
 * @param {string} toggleClass Class to be toggled.
 * @param {boolean} onElement Whether or not to remove the class from the panel
 *     element instead of the container.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#updatePosition
 * @description
 * Updates the position configuration of a panel. Use this to update the
 * position of a panel that is open, without having to close and re-open the
 * panel.
 *
 * @param {!MdPanelPosition} position
 */

/**
 * @ngdoc method
 * @name MdPanelRef#addToGroup
 * @description
 * Adds a panel to a group if the panel does not exist within the group already.
 * A panel can only exist within a single group.
 *
 * @param {string} groupName The name of the group to add the panel to.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#removeFromGroup
 * @description
 * Removes a panel from a group if the panel exists within that group. The group
 * must be created ahead of time.
 *
 * @param {string} groupName The name of the group.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#registerInterceptor
 * @description
 * Registers an interceptor with the panel. The callback should return a promise,
 * which will allow the action to continue when it gets resolved, or will
 * prevent an action if it is rejected. The interceptors are called sequentially
 * and it reverse order. `type` must be one of the following
 * values available on `$mdPanel.interceptorTypes`:
 * * `CLOSE` - Gets called before the panel begins closing.
 *
 * @param {string} type Type of interceptor.
 * @param {!Q.IPromise<any>} callback Callback to be registered.
 * @returns {!MdPanelRef}
 */

/**
 * @ngdoc method
 * @name MdPanelRef#removeInterceptor
 * @description
 * Removes a registered interceptor.
 *
 * @param {string} type Type of interceptor to be removed.
 * @param {function(): !Q.IPromise<any>} callback Interceptor to be removed.
 * @returns {!MdPanelRef}
 */

/**
 * @ngdoc method
 * @name MdPanelRef#removeAllInterceptors
 * @description
 * Removes all interceptors. If a type is supplied, only the
 * interceptors of that type will be cleared.
 *
 * @param {string=} type Type of interceptors to be removed.
 * @returns {!MdPanelRef}
 */

/**
 * @ngdoc method
 * @name MdPanelRef#updateAnimation
 * @description
 * Updates the animation configuration for a panel. You can use this to change
 * the panel's animation without having to re-create it.
 *
 * @param {!MdPanelAnimation} animation
 */


/*****************************************************************************
 *                               MdPanelPosition                            *
 *****************************************************************************/


/**
 * @ngdoc type
 * @name MdPanelPosition
 * @module material.components.panel
 * @description
 *
 * Object for configuring the position of the panel.
 *
 * @usage
 *
 * #### Centering the panel
 *
 * <hljs lang="js">
 * new MdPanelPosition().absolute().center();
 * </hljs>
 *
 * #### Overlapping the panel with an element
 *
 * <hljs lang="js">
 * new MdPanelPosition()
 *     .relativeTo(someElement)
 *     .addPanelPosition(
 *       $mdPanel.xPosition.ALIGN_START,
 *       $mdPanel.yPosition.ALIGN_TOPS
 *     );
 * </hljs>
 *
 * #### Aligning the panel with the bottom of an element
 *
 * <hljs lang="js">
 * new MdPanelPosition()
 *     .relativeTo(someElement)
 *     .addPanelPosition($mdPanel.xPosition.CENTER, $mdPanel.yPosition.BELOW);
 * </hljs>
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#absolute
 * @description
 * Positions the panel absolutely relative to the parent element. If the parent
 * is document.body, this is equivalent to positioning the panel absolutely
 * within the viewport.
 *
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#relativeTo
 * @description
 * Positions the panel relative to a specific element.
 *
 * @param {string|!Element|!JQLite} element Query selector, DOM element,
 *     or angular element to position the panel with respect to.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#top
 * @description
 * Sets the value of `top` for the panel. Clears any previously set vertical
 * position.
 *
 * @param {string=} top Value of `top`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#bottom
 * @description
 * Sets the value of `bottom` for the panel. Clears any previously set vertical
 * position.
 *
 * @param {string=} bottom Value of `bottom`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#start
 * @description
 * Sets the panel to the start of the page - `left` if `ltr` or `right` for
 * `rtl`. Clears any previously set horizontal position.
 *
 * @param {string=} start Value of position. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#end
 * @description
 * Sets the panel to the end of the page - `right` if `ltr` or `left` for `rtl`.
 * Clears any previously set horizontal position.
 *
 * @param {string=} end Value of position. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#left
 * @description
 * Sets the value of `left` for the panel. Clears any previously set
 * horizontal position.
 *
 * @param {string=} left Value of `left`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#right
 * @description
 * Sets the value of `right` for the panel. Clears any previously set
 * horizontal position.
 *
 * @param {string=} right Value of `right`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#centerHorizontally
 * @description
 * Centers the panel horizontally in the viewport. Clears any previously set
 * horizontal position.
 *
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#centerVertically
 * @description
 * Centers the panel vertically in the viewport. Clears any previously set
 * vertical position.
 *
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#center
 * @description
 * Centers the panel horizontally and vertically in the viewport. This is
 * equivalent to calling both `centerHorizontally` and `centerVertically`.
 * Clears any previously set horizontal and vertical positions.
 *
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#addPanelPosition
 * @description
 * Sets the x and y position for the panel relative to another element. Can be
 * called multiple times to specify an ordered list of panel positions. The
 * first position which allows the panel to be completely on-screen will be
 * chosen; the last position will be chose whether it is on-screen or not.
 *
 * xPosition must be one of the following values available on
 * $mdPanel.xPosition:
 *
 *
 * CENTER | ALIGN_START | ALIGN_END | OFFSET_START | OFFSET_END
 *
 * <pre>
 *    *************
 *    *           *
 *    *   PANEL   *
 *    *           *
 *    *************
 *   A B    C    D E
 *
 * A: OFFSET_START (for LTR displays)
 * B: ALIGN_START (for LTR displays)
 * C: CENTER
 * D: ALIGN_END (for LTR displays)
 * E: OFFSET_END (for LTR displays)
 * </pre>
 *
 * yPosition must be one of the following values available on
 * $mdPanel.yPosition:
 *
 * CENTER | ALIGN_TOPS | ALIGN_BOTTOMS | ABOVE | BELOW
 *
 * <pre>
 *   F
 *   G *************
 *     *           *
 *   H *   PANEL   *
 *     *           *
 *   I *************
 *   J
 *
 * F: BELOW
 * G: ALIGN_TOPS
 * H: CENTER
 * I: ALIGN_BOTTOMS
 * J: ABOVE
 * </pre>
 *
 * @param {string} xPosition
 * @param {string} yPosition
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#withOffsetX
 * @description
 * Sets the value of the offset in the x-direction.
 *
 * @param {string|number} offsetX
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#withOffsetY
 * @description
 * Sets the value of the offset in the y-direction.
 *
 * @param {string|number} offsetY
 * @returns {!MdPanelPosition}
 */


/*****************************************************************************
 *                               MdPanelAnimation                            *
 *****************************************************************************/


/**
 * @ngdoc type
 * @name MdPanelAnimation
 * @module material.components.panel
 * @description
 * Animation configuration object. To use, create an MdPanelAnimation with the
 * desired properties, then pass the object as part of $mdPanel creation.
 *
 * @usage
 *
 * <hljs lang="js">
 * var panelAnimation = new MdPanelAnimation()
 *     .openFrom(myButtonEl)
 *     .duration(1337)
 *     .closeTo('.my-button')
 *     .withAnimation($mdPanel.animation.SCALE);
 *
 * $mdPanel.create({
 *   animation: panelAnimation
 * });
 * </hljs>
 */

/**
 * @ngdoc method
 * @name MdPanelAnimation#openFrom
 * @description
 * Specifies where to start the open animation. `openFrom` accepts a
 * click event object, query selector, DOM element, or a Rect object that
 * is used to determine the bounds. When passed a click event, the location
 * of the click will be used as the position to start the animation.
 *
 * @param {string|!Element|!Event|{top: number, left: number}}
 * @returns {!MdPanelAnimation}
 */

/**
 * @ngdoc method
 * @name MdPanelAnimation#closeTo
 * @description
 * Specifies where to animate the panel close. `closeTo` accepts a
 * query selector, DOM element, or a Rect object that is used to determine
 * the bounds.
 *
 * @param {string|!Element|{top: number, left: number}}
 * @returns {!MdPanelAnimation}
 */

/**
 * @ngdoc method
 * @name MdPanelAnimation#withAnimation
 * @description
 * Specifies the animation class.
 *
 * There are several default animations that can be used: `$mdPanel.animation.`
 *  - `SLIDE`: The panel slides in and out from the specified
 *       elements. It will not fade in or out.
 *  - `SCALE`: The panel scales in and out. Slide and fade are
 *       included in this animation.
 *  - `FADE`: The panel fades in and out.
 *
 * Custom classes will by default fade in and out unless
 * `transition: opacity 1ms` is added to the to custom class.
 *
 * @param {string|{open: string, close: string}} cssClass
 * @returns {!MdPanelAnimation}
 */

/**
 * @ngdoc method
 * @name MdPanelAnimation#duration
 * @description
 * Specifies the duration of the animation in milliseconds. The `duration`
 * method accepts either a number or an object with separate open and close
 * durations.
 *
 * @param {number|{open: number, close: number}} duration
 * @returns {!MdPanelAnimation}
 */


/*****************************************************************************
 *                            PUBLIC DOCUMENTATION                           *
 *****************************************************************************/


var MD_PANEL_Z_INDEX = 80;
var MD_PANEL_HIDDEN = '_md-panel-hidden';
var FOCUS_TRAP_TEMPLATE = angular.element(
    '<div class="_md-panel-focus-trap" tabindex="0"></div>');

var _presets = {};


/**
 * A provider that is used for creating presets for the panel API.
 * @final @constructor @ngInject
 */
function MdPanelProvider() {
  return {
    'definePreset': definePreset,
    'getAllPresets': getAllPresets,
    'clearPresets': clearPresets,
    '$get': $getProvider()
  };
}


/**
 * Takes the passed in panel configuration object and adds it to the `_presets`
 * object at the specified name.
 * @param {string} name Name of the preset to set.
 * @param {!Object} preset Specific configuration object that can contain any
 *     and all of the parameters available within the `$mdPanel.create` method.
 *     However, parameters that pertain to id, position, animation, and user
 *     interaction are not allowed and will be removed from the preset
 *     configuration.
 */
function definePreset(name, preset) {
  if (!name || !preset) {
    throw new Error('mdPanelProvider: The panel preset definition is ' +
        'malformed. The name and preset object are required.');
  } else if (_presets.hasOwnProperty(name)) {
    throw new Error('mdPanelProvider: The panel preset you have requested ' +
        'has already been defined.');
  }

  // Delete any property on the preset that is not allowed.
  delete preset.id;
  delete preset.position;
  delete preset.animation;

  _presets[name] = preset;
}


/**
 * Gets a clone of the `_presets`.
 * @return {!Object}
 */
function getAllPresets() {
  return angular.copy(_presets);
}


/**
 * Clears all of the stored presets.
 */
function clearPresets() {
  _presets = {};
}


/**
 * Represents the `$get` method of the AngularJS provider. From here, a new
 * reference to the MdPanelService is returned where the needed arguments are
 * passed in including the MdPanelProvider `_presets`.
 * @param {!Object} _presets
 * @param {!JQLite} $rootElement
 * @param {!angular.Scope} $rootScope
 * @param {!IInjectorService} $injector
 * @param {!IWindowService} $window
 */
function $getProvider() {
  return [
    '$rootElement', '$rootScope', '$injector', '$window',
    function($rootElement, $rootScope, $injector, $window) {
      return new MdPanelService(_presets, $rootElement, $rootScope,
          $injector, $window);
    }
  ];
}


/*****************************************************************************
 *                               MdPanel Service                             *
 *****************************************************************************/


/**
 * A service that is used for controlling/displaying panels on the screen.
 * @param {!Object} presets
 * @param {!JQLite} $rootElement
 * @param {!angular.Scope} $rootScope
 * @param {!IInjectorService} $injector
 * @param {!IWindowService} $window
 * @final @constructor @ngInject
 */
function MdPanelService(presets, $rootElement, $rootScope, $injector, $window) {
  /**
   * Default config options for the panel.
   * Anything angular related needs to be done later. Therefore
   *     scope: $rootScope.$new(true),
   *     attachTo: $rootElement,
   * are added later.
   * @private {!Object}
   */
  this._defaultConfigOptions = {
    bindToController: true,
    clickOutsideToClose: false,
    disableParentScroll: false,
    escapeToClose: false,
    focusOnOpen: true,
    fullscreen: false,
    hasBackdrop: false,
    propagateContainerEvents: false,
    transformTemplate: angular.bind(this, this._wrapTemplate),
    trapFocus: false,
    zIndex: MD_PANEL_Z_INDEX
  };

  /** @private {!Object} */
  this._config = {};

  /** @private {!Object} */
  this._presets = presets;

  /** @private @const */
  this._$rootElement = $rootElement;

  /** @private @const */
  this._$rootScope = $rootScope;

  /** @private @const */
  this._$injector = $injector;

  /** @private @const */
  this._$window = $window;

  /** @private @const */
  this._$mdUtil = this._$injector.get('$mdUtil');

  /** @private {!Object<string, !MdPanelRef>} */
  this._trackedPanels = {};

  /**
   * @private {!Object<string,
   *     {panels: !Array<!MdPanelRef>,
   *     openPanels: !Array<!MdPanelRef>,
   *     maxOpen: number}>}
   */
  this._groups = Object.create(null);

  /**
   * Default animations that can be used within the panel.
   * @type {enum}
   */
  this.animation = MdPanelAnimation.animation;

  /**
   * Possible values of xPosition for positioning the panel relative to
   * another element.
   * @type {enum}
   */
  this.xPosition = MdPanelPosition.xPosition;

  /**
   * Possible values of yPosition for positioning the panel relative to
   * another element.
   * @type {enum}
   */
  this.yPosition = MdPanelPosition.yPosition;

  /**
   * Possible values for the interceptors that can be registered on a panel.
   * @type {enum}
   */
  this.interceptorTypes = MdPanelRef.interceptorTypes;

  /**
   * Possible values for closing of a panel.
   * @type {enum}
   */
  this.closeReasons = MdPanelRef.closeReasons;

  /**
   * Possible values of absolute position.
   * @type {enum}
   */
  this.absPosition = MdPanelPosition.absPosition;
}


/**
 * Creates a panel with the specified options.
 * @param {string|Object=} preset Name of a preset configuration that can be used to
 *     extend the panel configuration.
 * @param {!Object=} config Configuration object for the panel.
 * @returns {!MdPanelRef}
 */
MdPanelService.prototype.create = function(preset, config) {
  if (typeof preset === 'string') {
    preset = this._getPresetByName(preset);
  } else if (typeof preset === 'object' &&
      (angular.isUndefined(config) || !config)) {
    config = preset;
    preset = {};
  }

  preset = preset || {};
  config = config || {};

  // If the passed-in config contains an ID and the ID is within _trackedPanels,
  // return the tracked panel after updating its config with the passed-in
  // config.
  if (angular.isDefined(config.id) && this._trackedPanels[config.id]) {
    var trackedPanel = this._trackedPanels[config.id];
    angular.extend(trackedPanel.config, config);
    return trackedPanel;
  }

  // Combine the passed-in config, the _defaultConfigOptions, and the preset
  // configuration into the `_config`.
  this._config = angular.extend({
    // If no ID is set within the passed-in config, then create an arbitrary ID.
    id: config.id || 'panel_' + this._$mdUtil.nextUid(),
    scope: this._$rootScope.$new(true),
    attachTo: this._$rootElement
  }, this._defaultConfigOptions, config, preset);

  // Create the panelRef and add it to the `_trackedPanels` object.
  var panelRef = new MdPanelRef(this._config, this._$injector);
  this._trackedPanels[this._config.id] = panelRef;

  // Add the panel to each of its requested groups.
  if (this._config.groupName) {
    if (angular.isString(this._config.groupName)) {
      this._config.groupName = [this._config.groupName];
    }
    angular.forEach(this._config.groupName, function(group) {
      panelRef.addToGroup(group);
    });
  }

  this._config.scope.$on('$destroy', angular.bind(panelRef, panelRef.detach));

  return panelRef;
};


/**
 * Creates and opens a panel with the specified options.
 * @param {string=} preset Name of a preset configuration that can be used to
 *     extend the panel configuration.
 * @param {!Object=} config Configuration object for the panel.
 * @returns {!Q.IPromise<!MdPanelRef>} The panel created from create.
 */
MdPanelService.prototype.open = function(preset, config) {
  var panelRef = this.create(preset, config);
  return panelRef.open().then(function() {
    return panelRef;
  });
};


/**
 * Gets a specific preset configuration object saved within `_presets`.
 * @param {string} preset Name of the preset to search for.
 * @returns {!Object} The preset configuration object.
 */
MdPanelService.prototype._getPresetByName = function(preset) {
  if (!this._presets[preset]) {
    throw new Error('mdPanel: The panel preset configuration that you ' +
        'requested does not exist. Use the $mdPanelProvider to create a ' +
        'preset before requesting one.');
  }
  return this._presets[preset];
};


/**
 * Returns a new instance of the MdPanelPosition. Use this to create the
 * positioning object.
 * @returns {!MdPanelPosition}
 */
MdPanelService.prototype.newPanelPosition = function() {
  return new MdPanelPosition(this._$injector);
};


/**
 * Returns a new instance of the MdPanelAnimation. Use this to create the
 * animation object.
 * @returns {!MdPanelAnimation}
 */
MdPanelService.prototype.newPanelAnimation = function() {
  return new MdPanelAnimation(this._$injector);
};


/**
 * Creates a panel group and adds it to a tracked list of panel groups.
 * @param groupName {string} Name of the group to create.
 * @param config {!Object=} Specific configuration object that may contain the
 *     following properties:
 *
 *   - `maxOpen` - `{number=}`: The maximum number of panels that are allowed
 *     open within a defined panel group.
 *
 * @returns {!Object<string,
 *     {panels: !Array<!MdPanelRef>,
 *     openPanels: !Array<!MdPanelRef>,
 *     maxOpen: number}>} panelGroup
 */
MdPanelService.prototype.newPanelGroup = function(groupName, config) {
  if (!this._groups[groupName]) {
    config = config || {};
    var group = {
      panels: [],
      openPanels: [],
      maxOpen: config.maxOpen > 0 ? config.maxOpen : Infinity
    };
    this._groups[groupName] = group;
  }
  return this._groups[groupName];
};


/**
 * Sets the maximum number of panels in a group that can be opened at a given
 * time.
 * @param {string} groupName The name of the group to configure.
 * @param {number} maxOpen The maximum number of panels that can be
 *     opened. Infinity can be passed in to remove the maxOpen limit.
 */
MdPanelService.prototype.setGroupMaxOpen = function(groupName, maxOpen) {
  if (this._groups[groupName]) {
    this._groups[groupName].maxOpen = maxOpen;
  } else {
    throw new Error('mdPanel: Group does not exist yet. Call newPanelGroup().');
  }
};


/**
 * Determines if the current number of open panels within a group exceeds the
 * limit of allowed open panels.
 * @param {string} groupName The name of the group to check.
 * @returns {boolean} true if open count does exceed maxOpen and false if not.
 * @private
 */
MdPanelService.prototype._openCountExceedsMaxOpen = function(groupName) {
  if (this._groups[groupName]) {
    var group = this._groups[groupName];
    return group.maxOpen > 0 && group.openPanels.length > group.maxOpen;
  }
  return false;
};


/**
 * Closes the first open panel within a specific group.
 * @param {string} groupName The name of the group.
 * @private
 */
MdPanelService.prototype._closeFirstOpenedPanel = function(groupName) {
  this._groups[groupName].openPanels[0].close();
};


/**
 * Wraps the user's template in three elements:
 * - md-panel-outer-wrapper - covers the entire `attachTo` element.
 * - md-panel-inner-wrapper - handles the positioning.
 * - md-panel - contains the user's content and deals with the animations.
 * @param {string} origTemplate The original template.
 * @returns {string} The wrapped template.
 * @private
 */
MdPanelService.prototype._wrapTemplate = function(origTemplate) {
  var template = origTemplate || '';

  // The panel should be initially rendered offscreen so we can calculate
  // height and width for positioning.
  return '' +
      '<div class="md-panel-outer-wrapper">' +
        '<div class="md-panel-inner-wrapper _md-panel-offscreen">' +
          '<div class="md-panel _md-panel-offscreen">' + template + '</div>' +
        '</div>' +
      '</div>';
};


/**
 * Wraps a content element in a `md-panel-outer-wrapper`, as well as
 * a `md-panel-inner-wrapper`, and positions it off-screen. Allows for
 * proper control over positioning and animations.
 * @param {!JQLite} contentElement Element to be wrapped.
 * @return {!JQLite} Wrapper element.
 * @private
 */
MdPanelService.prototype._wrapContentElement = function(contentElement) {
  var outerWrapper = angular.element(
    '<div class="md-panel-outer-wrapper">' +
      '<div class="md-panel-inner-wrapper _md-panel-offscreen"></div>' +
    '</div>'
  );

  contentElement.addClass('md-panel _md-panel-offscreen');
  outerWrapper.children().eq(0).append(contentElement);

  return outerWrapper;
};


/*****************************************************************************
 *                                 MdPanelRef                                *
 *****************************************************************************/


/**
 * A reference to a created panel. This reference contains a unique id for the
 * panel, along with properties/functions used to control the panel.
 * @param {!Object} config
 * @param {!IInjectorService} $injector
 * @final @constructor
 */
function MdPanelRef(config, $injector) {
  // Injected variables.
  /** @private @const {!IQService} */
  this._$q = $injector.get('$q');

  /** @private @const {!angular.$mdCompiler} */
  this._$mdCompiler = $injector.get('$mdCompiler');

  /** @private @const {!angular.$mdConstant} */
  this._$mdConstant = $injector.get('$mdConstant');

  /** @private @const {!angular.$mdUtil} */
  this._$mdUtil = $injector.get('$mdUtil');

  /** @private @const {!angular.$mdTheming} */
  this._$mdTheming = $injector.get('$mdTheming');

  /** @private @const {!IRootScopeService} */
  this._$rootScope = $injector.get('$rootScope');

  /** @private @const {!angular.$animate} */
  this._$animate = $injector.get('$animate');

  /** @private @const {!MdPanelRef} */
  this._$mdPanel = $injector.get('$mdPanel');

  /** @private @const {!ILogService} */
  this._$log = $injector.get('$log');

  /** @private @const {!IWindowService} */
  this._$window = $injector.get('$window');

  /** @private @const {!Function} */
  this._$$rAF = $injector.get('$$rAF');

  // Public variables.
  /**
   * Unique id for the panelRef.
   * @type {string}
   */
  this.id = config.id;

  /** @type {!Object} */
  this.config = config;

  /** @type {!JQLite|undefined} */
  this.panelContainer = undefined;

  /** @type {!JQLite|undefined} */
  this.panelEl = undefined;

  /** @type {!JQLite|undefined} */
  this.innerWrapper = undefined;

  /**
   * Whether the panel is attached. This is synchronous. When attach is called,
   * isAttached is set to true. When detach is called, isAttached is set to
   * false.
   * @type {boolean}
   */
  this.isAttached = false;

  // Private variables.
  /** @private {Array<function()>} */
  this._removeListeners = [];

  /** @private {!JQLite|undefined} */
  this._topFocusTrap = undefined;

  /** @private {!JQLite|undefined} */
  this._bottomFocusTrap = undefined;

  /** @private {!$mdPanel|undefined} */
  this._backdropRef = undefined;

  /** @private {Function?} */
  this._restoreScroll = null;

  /**
   * Keeps track of all the panel interceptors.
   * @private {!Object}
   */
  this._interceptors = Object.create(null);

  /**
   * Cleanup function, provided by `$mdCompiler` and assigned after the element
   * has been compiled. When `contentElement` is used, the function is used to
   * restore the element to it's proper place in the DOM.
   * @private {Function|null}
   */
  this._compilerCleanup = null;

  /**
   * Cache for saving and restoring element inline styles, CSS classes etc.
   * @type {{styles: string, classes: string}}
   */
  this._restoreCache = {
    styles: '',
    classes: ''
  };
}


MdPanelRef.interceptorTypes = {
  CLOSE: 'onClose'
};


/**
 * Opens an already created and configured panel. If the panel is already
 * visible, does nothing.
 * @returns {!Q.IPromise<!MdPanelRef>} A promise that is resolved when
 *     the panel is opened and animations finish.
 */
MdPanelRef.prototype.open = function() {
  var self = this;
  return this._$q(function(resolve, reject) {
    var done = self._done(resolve, self);
    var show = self._simpleBind(self.show, self);
    var checkGroupMaxOpen = function() {
      if (self.config.groupName) {
        angular.forEach(self.config.groupName, function(group) {
          if (self._$mdPanel._openCountExceedsMaxOpen(group)) {
            self._$mdPanel._closeFirstOpenedPanel(group);
          }
        });
      }
    };

    self.attach()
        .then(show)
        .then(checkGroupMaxOpen)
        .then(done)
        .catch(reject);
  });
};


/**
 * Closes the panel.
 * @param {string} closeReason The event type that triggered the close.
 * @returns {!Q.IPromise<!MdPanelRef>} A promise that is resolved when
 *     the panel is closed and animations finish.
 */
MdPanelRef.prototype.close = function(closeReason) {
  var self = this;

  return this._$q(function(resolve, reject) {
    self._callInterceptors(MdPanelRef.interceptorTypes.CLOSE).then(function() {
      var done = self._done(resolve, self);
      var detach = self._simpleBind(self.detach, self);
      var onCloseSuccess = self.config['onCloseSuccess'] || angular.noop;
      onCloseSuccess = angular.bind(self, onCloseSuccess, self, closeReason);

      self.hide()
          .then(detach)
          .then(done)
          .then(onCloseSuccess)
          .catch(reject);
    }, reject);
  });
};


/**
 * Attaches the panel. The panel will be hidden afterwards.
 * @returns {!Q.IPromise<!MdPanelRef>} A promise that is resolved when
 *     the panel is attached.
 */
MdPanelRef.prototype.attach = function() {
  if (this.isAttached && this.panelEl) {
    return this._$q.when(this);
  }

  var self = this;
  return this._$q(function(resolve, reject) {
    var done = self._done(resolve, self);
    var onDomAdded = self.config['onDomAdded'] || angular.noop;
    var addListeners = function(response) {
      self.isAttached = true;
      self._addEventListeners();
      return response;
    };

    self._$q.all([
        self._createBackdrop(),
        self._createPanel()
            .then(addListeners)
            .catch(reject)
    ]).then(onDomAdded)
      .then(done)
      .catch(reject);
  });
};


/**
 * Only detaches the panel. Will NOT hide the panel first.
 * @returns {!Q.IPromise<!MdPanelRef>} A promise that is resolved when
 *     the panel is detached.
 */
MdPanelRef.prototype.detach = function() {
  if (!this.isAttached) {
    return this._$q.when(this);
  }

  var self = this;
  var onDomRemoved = self.config['onDomRemoved'] || angular.noop;

  var detachFn = function() {
    self._removeEventListeners();

    // Remove the focus traps that we added earlier for keeping focus within
    // the panel.
    if (self._topFocusTrap && self._topFocusTrap.parentNode) {
      self._topFocusTrap.parentNode.removeChild(self._topFocusTrap);
    }

    if (self._bottomFocusTrap && self._bottomFocusTrap.parentNode) {
      self._bottomFocusTrap.parentNode.removeChild(self._bottomFocusTrap);
    }

    if (self._restoreCache.classes) {
      self.panelEl[0].className = self._restoreCache.classes;
    }

    // Either restore the saved styles or clear the ones set by mdPanel.
    self.panelEl[0].style.cssText = self._restoreCache.styles || '';

    self._compilerCleanup();
    self.panelContainer.remove();
    self.isAttached = false;
    return self._$q.when(self);
  };

  if (this._restoreScroll) {
    this._restoreScroll();
    this._restoreScroll = null;
  }

  return this._$q(function(resolve, reject) {
    var done = self._done(resolve, self);

    self._$q.all([
      detachFn(),
      self._backdropRef ? self._backdropRef.detach() : true
    ]).then(onDomRemoved)
      .then(done)
      .catch(reject);
  });
};


/**
 * Destroys the panel. The Panel cannot be opened again after this.
 */
MdPanelRef.prototype.destroy = function() {
  var self = this;
  if (this.config.groupName) {
    angular.forEach(this.config.groupName, function(group) {
      self.removeFromGroup(group);
    });
  }
  this.config.scope.$destroy();
  this.config.locals = null;
  this.config.onDomAdded = null;
  this.config.onDomRemoved = null;
  this.config.onRemoving = null;
  this.config.onOpenComplete = null;
  this._interceptors = undefined;
};


/**
 * Shows the panel.
 * @returns {!Q.IPromise<!MdPanelRef>} A promise that is resolved when
 *     the panel has shown and animations finish.
 */
MdPanelRef.prototype.show = function() {
  if (!this.panelContainer) {
    return this._$q(function(resolve, reject) {
      reject('mdPanel: Panel does not exist yet. Call open() or attach().');
    });
  }

  if (!this.panelContainer.hasClass(MD_PANEL_HIDDEN)) {
    return this._$q.when(this);
  }

  var self = this;
  var animatePromise = function() {
    self.panelContainer.removeClass(MD_PANEL_HIDDEN);
    return self._animateOpen();
  };

  return this._$q(function(resolve, reject) {
    var done = self._done(resolve, self);
    var onOpenComplete = self.config['onOpenComplete'] || angular.noop;
    var addToGroupOpen = function() {
      if (self.config.groupName) {
        angular.forEach(self.config.groupName, function(group) {
          self._$mdPanel._groups[group].openPanels.push(self);
        });
      }
    };

    self._$q.all([
      self._backdropRef ? self._backdropRef.show() : self,
      animatePromise().then(function() { self._focusOnOpen(); }, reject)
    ]).then(onOpenComplete)
      .then(addToGroupOpen)
      .then(done)
      .catch(reject);
  });
};


/**
 * Hides the panel.
 * @returns {!Q.IPromise<!MdPanelRef>} A promise that is resolved when
 *     the panel has hidden and animations finish.
 */
MdPanelRef.prototype.hide = function() {
  if (!this.panelContainer) {
    return this._$q(function(resolve, reject) {
      reject('mdPanel: Panel does not exist yet. Call open() or attach().');
    });
  }

  if (this.panelContainer.hasClass(MD_PANEL_HIDDEN)) {
    return this._$q.when(this);
  }

  var self = this;

  return this._$q(function(resolve, reject) {
    var done = self._done(resolve, self);
    var onRemoving = self.config['onRemoving'] || angular.noop;
    var hidePanel = function() {
      self.panelContainer.addClass(MD_PANEL_HIDDEN);
    };
    var removeFromGroupOpen = function() {
      if (self.config.groupName) {
        var index;
        angular.forEach(self.config.groupName, function(group) {
          group = self._$mdPanel._groups[group];
          index = group.openPanels.indexOf(self);
          if (index > -1) {
            group.openPanels.splice(index, 1);
          }
        });
      }
    };
    var focusOnOrigin = function() {
      var origin = self.config['origin'];
      if (origin) {
        getElement(origin).focus();
      }
    };

    self._$q.all([
      self._backdropRef ? self._backdropRef.hide() : self,
      self._animateClose()
          .then(onRemoving)
          .then(hidePanel)
          .then(removeFromGroupOpen)
          .then(focusOnOrigin)
          .catch(reject)
    ]).then(done, reject);
  });
};


/**
 * Add a class to the panel. DO NOT use this to hide/show the panel.
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 *
 * @param {string} newClass Class to be added.
 * @param {boolean} toElement Whether or not to add the class to the panel
 *     element instead of the container.
 */
MdPanelRef.prototype.addClass = function(newClass, toElement) {
  this._$log.warn(
      'mdPanel: The addClass method is in the process of being deprecated. ' +
      'Full deprecation is scheduled for the AngularJS Material 1.2 release. ' +
      'To achieve the same results, use the panelContainer or panelEl ' +
      'JQLite elements that are referenced in MdPanelRef.');

  if (!this.panelContainer) {
    throw new Error(
        'mdPanel: Panel does not exist yet. Call open() or attach().');
  }

  if (!toElement && !this.panelContainer.hasClass(newClass)) {
    this.panelContainer.addClass(newClass);
  } else if (toElement && !this.panelEl.hasClass(newClass)) {
    this.panelEl.addClass(newClass);
  }
};


/**
 * Remove a class from the panel. DO NOT use this to hide/show the panel.
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 *
 * @param {string} oldClass Class to be removed.
 * @param {boolean} fromElement Whether or not to remove the class from the
 *     panel element instead of the container.
 */
MdPanelRef.prototype.removeClass = function(oldClass, fromElement) {
  this._$log.warn(
      'mdPanel: The removeClass method is in the process of being deprecated. ' +
      'Full deprecation is scheduled for the AngularJS Material 1.2 release. ' +
      'To achieve the same results, use the panelContainer or panelEl ' +
      'JQLite elements that are referenced in MdPanelRef.');

  if (!this.panelContainer) {
    throw new Error(
        'mdPanel: Panel does not exist yet. Call open() or attach().');
  }

  if (!fromElement && this.panelContainer.hasClass(oldClass)) {
    this.panelContainer.removeClass(oldClass);
  } else if (fromElement && this.panelEl.hasClass(oldClass)) {
    this.panelEl.removeClass(oldClass);
  }
};


/**
 * Toggle a class on the panel. DO NOT use this to hide/show the panel.
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 *
 * @param {string} toggleClass The class to toggle.
 * @param {boolean} onElement Whether or not to toggle the class on the panel
 *     element instead of the container.
 */
MdPanelRef.prototype.toggleClass = function(toggleClass, onElement) {
  this._$log.warn(
      'mdPanel: The toggleClass method is in the process of being deprecated. ' +
      'Full deprecation is scheduled for the AngularJS Material 1.2 release. ' +
      'To achieve the same results, use the panelContainer or panelEl ' +
      'JQLite elements that are referenced in MdPanelRef.');

  if (!this.panelContainer) {
    throw new Error(
        'mdPanel: Panel does not exist yet. Call open() or attach().');
  }

  if (!onElement) {
    this.panelContainer.toggleClass(toggleClass);
  } else {
    this.panelEl.toggleClass(toggleClass);
  }
};


/**
 * Compiles the panel, according to the passed in config and appends it to
 * the DOM. Helps normalize differences in the compilation process between
 * using a string template and a content element.
 * @returns {!Q.IPromise<!MdPanelRef>} Promise that is resolved when
 *     the element has been compiled and added to the DOM.
 * @private
 */
MdPanelRef.prototype._compile = function() {
  var self = this;

  // Compile the element via $mdCompiler. Note that when using a
  // contentElement, the element isn't actually being compiled, rather the
  // compiler saves it's place in the DOM and provides a way of restoring it.
  return self._$mdCompiler.compile(self.config).then(function(compileData) {
    var config = self.config;

    if (config.contentElement) {
      var panelEl = compileData.element;

      // Since mdPanel modifies the inline styles and CSS classes, we need
      // to save them in order to be able to restore on close.
      self._restoreCache.styles = panelEl[0].style.cssText;
      self._restoreCache.classes = panelEl[0].className;

      self.panelContainer = self._$mdPanel._wrapContentElement(panelEl);
      self.panelEl = panelEl;
    } else {
      self.panelContainer = compileData.link(config['scope']);
      self.panelEl = angular.element(
        self.panelContainer[0].querySelector('.md-panel')
      );
    }

    // Save a reference to the inner wrapper.
    self.innerWrapper = angular.element(
      self.panelContainer[0].querySelector('.md-panel-inner-wrapper')
    );

    // Save a reference to the cleanup function from the compiler.
    self._compilerCleanup = compileData.cleanup;

    // Attach the panel to the proper place in the DOM.
    getElement(self.config['attachTo']).append(self.panelContainer);

    return self;
  });
};


/**
 * Creates a panel and adds it to the dom.
 * @returns {!Q.IPromise} A promise that is resolved when the panel is
 *     created.
 * @private
 */
MdPanelRef.prototype._createPanel = function() {
  var self = this;

  return this._$q(function(resolve, reject) {
    if (!self.config.locals) {
      self.config.locals = {};
    }

    self.config.locals.mdPanelRef = self;

    self._compile().then(function() {
      if (self.config['disableParentScroll']) {
        self._restoreScroll = self._$mdUtil.disableScrollAround(
          null,
          self.panelContainer,
          { disableScrollMask: true }
        );
      }

      // Add a custom CSS class to the panel element.
      if (self.config['panelClass']) {
        self.panelEl.addClass(self.config['panelClass']);
      }

      // Handle click and touch events for the panel container.
      if (self.config['propagateContainerEvents']) {
        self.panelContainer.css('pointer-events', 'none');
        self.panelEl.css('pointer-events', 'all');
      }

      // Panel may be outside the $rootElement, tell ngAnimate to animate
      // regardless.
      if (self._$animate.pin) {
        self._$animate.pin(
          self.panelContainer,
          getElement(self.config['attachTo'])
        );
      }

      self._configureTrapFocus();
      self._addStyles().then(function() {
        resolve(self);
      }, reject);
    }, reject);

  });
};


/**
 * Adds the styles for the panel, such as positioning and z-index. Also,
 * themes the panel element and panel container using `$mdTheming`.
 * @returns {!Q.IPromise<!MdPanelRef>}
 * @private
 */
MdPanelRef.prototype._addStyles = function() {
  var self = this;
  return this._$q(function(resolve) {
    self.panelContainer.css('z-index', self.config['zIndex']);
    self.innerWrapper.css('z-index', self.config['zIndex'] + 1);

    var hideAndResolve = function() {
      // Theme the element and container.
      self._setTheming();

      // Remove offscreen classes and add hidden class.
      self.panelEl.removeClass('_md-panel-offscreen');
      self.innerWrapper.removeClass('_md-panel-offscreen');
      self.panelContainer.addClass(MD_PANEL_HIDDEN);

      resolve(self);
    };

    if (self.config['fullscreen']) {
      self.panelEl.addClass('_md-panel-fullscreen');
      hideAndResolve();
      return; // Don't setup positioning.
    }

    var positionConfig = self.config['position'];
    if (!positionConfig) {
      hideAndResolve();
      return; // Don't setup positioning.
    }

    // Wait for angular to finish processing the template
    self._$rootScope['$$postDigest'](function() {
      // Position it correctly. This is necessary so that the panel will have a
      // defined height and width.
      self._updatePosition(true);

      // Theme the element and container.
      self._setTheming();

      resolve(self);
    });
  });
};


/**
 * Sets the `$mdTheming` classes on the `panelContainer` and `panelEl`.
 * @private
 */
MdPanelRef.prototype._setTheming = function() {
  this._$mdTheming(this.panelEl);
  this._$mdTheming(this.panelContainer);
};


/**
 * Updates the position configuration of a panel
 * @param {!MdPanelPosition} position
 */
MdPanelRef.prototype.updatePosition = function(position) {
  if (!this.panelContainer) {
    throw new Error(
        'mdPanel: Panel does not exist yet. Call open() or attach().');
  }

  this.config['position'] = position;
  this._updatePosition();
};


/**
 * Calculates and updates the position of the panel.
 * @param {boolean=} init
 * @private
 */
MdPanelRef.prototype._updatePosition = function(init) {
  var positionConfig = this.config['position'];

  if (positionConfig) {
    positionConfig._setPanelPosition(this.innerWrapper);

    // Hide the panel now that position is known.
    if (init) {
      this.panelEl.removeClass('_md-panel-offscreen');
      this.innerWrapper.removeClass('_md-panel-offscreen');
      this.panelContainer.addClass(MD_PANEL_HIDDEN);
    }

    this.innerWrapper.css(
      MdPanelPosition.absPosition.TOP,
      positionConfig.getTop()
    );
    this.innerWrapper.css(
      MdPanelPosition.absPosition.BOTTOM,
      positionConfig.getBottom()
    );
    this.innerWrapper.css(
      MdPanelPosition.absPosition.LEFT,
      positionConfig.getLeft()
    );
    this.innerWrapper.css(
      MdPanelPosition.absPosition.RIGHT,
      positionConfig.getRight()
    );
  }
};


/**
 * Focuses on the panel or the first focus target.
 * @private
 */
MdPanelRef.prototype._focusOnOpen = function() {
  if (this.config['focusOnOpen']) {
    // Wait for the template to finish rendering to guarantee md-autofocus has
    // finished adding the class md-autofocus, otherwise the focusable element
    // isn't available to focus.
    var self = this;
    this._$rootScope['$$postDigest'](function() {
      var target = self._$mdUtil.findFocusTarget(self.panelEl) ||
          self.panelEl;
      target.focus();
    });
  }
};


/**
 * Shows the backdrop.
 * @returns {!Q.IPromise} A promise that is resolved when the backdrop
 *     is created and attached.
 * @private
 */
MdPanelRef.prototype._createBackdrop = function() {
  if (this.config.hasBackdrop) {
    if (!this._backdropRef) {
      var backdropAnimation = this._$mdPanel.newPanelAnimation()
          .openFrom(this.config.attachTo)
          .withAnimation({
            open: '_md-opaque-enter',
            close: '_md-opaque-leave'
          });

      if (this.config.animation) {
        backdropAnimation.duration(this.config.animation._rawDuration);
      }

      var backdropConfig = {
        animation: backdropAnimation,
        attachTo: this.config.attachTo,
        focusOnOpen: false,
        panelClass: '_md-panel-backdrop',
        zIndex: this.config.zIndex - 1
      };

      this._backdropRef = this._$mdPanel.create(backdropConfig);
    }
    if (!this._backdropRef.isAttached) {
      return this._backdropRef.attach();
    }
  }
};


/**
 * Listen for escape keys and outside clicks to auto close.
 * @private
 */
MdPanelRef.prototype._addEventListeners = function() {
  this._configureEscapeToClose();
  this._configureClickOutsideToClose();
  this._configureScrollListener();
};


/**
 * Remove event listeners added in _addEventListeners.
 * @private
 */
MdPanelRef.prototype._removeEventListeners = function() {
  this._removeListeners && this._removeListeners.forEach(function(removeFn) {
    removeFn();
  });
  this._removeListeners = [];
};


/**
 * Setup the escapeToClose event listeners.
 * @private
 */
MdPanelRef.prototype._configureEscapeToClose = function() {
  if (this.config['escapeToClose']) {
    var parentTarget = getElement(this.config['attachTo']);
    var self = this;

    var keyHandlerFn = function(ev) {
      if (ev.keyCode === self._$mdConstant.KEY_CODE.ESCAPE) {
        ev.stopPropagation();
        ev.preventDefault();

        self.close(MdPanelRef.closeReasons.ESCAPE);
      }
    };

    // Add keydown listeners
    this.panelContainer.on('keydown', keyHandlerFn);
    parentTarget.on('keydown', keyHandlerFn);

    // Queue remove listeners function
    this._removeListeners.push(function() {
      self.panelContainer.off('keydown', keyHandlerFn);
      parentTarget.off('keydown', keyHandlerFn);
    });
  }
};


/**
 * Setup the clickOutsideToClose event listeners.
 * @private
 */
MdPanelRef.prototype._configureClickOutsideToClose = function() {
  if (this.config['clickOutsideToClose']) {
    var target = this.config['propagateContainerEvents'] ?
        angular.element(document.body) :
        this.panelContainer;
    var sourceEl;

    // Keep track of the element on which the mouse originally went down
    // so that we can only close the backdrop when the 'click' started on it.
    // A simple 'click' handler does not work, it sets the target object as the
    // element the mouse went down on.
    var mousedownHandler = function(ev) {
      sourceEl = ev.target;
    };

    // We check if our original element and the target is the backdrop
    // because if the original was the backdrop and the target was inside the
    // panel we don't want to panel to close.
    var self = this;
    var mouseupHandler = function(ev) {
      if (self.config['propagateContainerEvents']) {

        // We check if the sourceEl of the event is the panel element or one
        // of it's children. If it is not, then close the panel.
        if (sourceEl !== self.panelEl[0] && !self.panelEl[0].contains(sourceEl)) {
          self.close();
        }

      } else if (sourceEl === target[0] && ev.target === target[0]) {
        ev.stopPropagation();
        ev.preventDefault();

        self.close(MdPanelRef.closeReasons.CLICK_OUTSIDE);
      }
    };

    // Add listeners
    target.on('mousedown', mousedownHandler);
    target.on('mouseup', mouseupHandler);

    // Queue remove listeners function
    this._removeListeners.push(function() {
      target.off('mousedown', mousedownHandler);
      target.off('mouseup', mouseupHandler);
    });
  }
};


/**
 * Configures the listeners for updating the panel position on scroll.
 * @private
*/
MdPanelRef.prototype._configureScrollListener = function() {
  // No need to bind the event if scrolling is disabled.
  if (!this.config['disableParentScroll']) {
    var updatePosition = angular.bind(this, this._updatePosition);
    var debouncedUpdatePosition = this._$$rAF.throttle(updatePosition);
    var self = this;

    var onScroll = function() {
      debouncedUpdatePosition();
    };

    // Add listeners.
    this._$window.addEventListener('scroll', onScroll, true);

    // Queue remove listeners function.
    this._removeListeners.push(function() {
      self._$window.removeEventListener('scroll', onScroll, true);
    });
  }
};


/**
 * Setup the focus traps. These traps will wrap focus when tabbing past the
 * panel. When shift-tabbing, the focus will stick in place.
 * @private
 */
MdPanelRef.prototype._configureTrapFocus = function() {
  // Focus doesn't remain inside of the panel without this.
  this.panelEl.attr('tabIndex', '-1');
  if (this.config['trapFocus']) {
    var element = this.panelEl;
    // Set up elements before and after the panel to capture focus and
    // redirect back into the panel.
    this._topFocusTrap = FOCUS_TRAP_TEMPLATE.clone()[0];
    this._bottomFocusTrap = FOCUS_TRAP_TEMPLATE.clone()[0];

    // When focus is about to move out of the panel, we want to intercept it
    // and redirect it back to the panel element.
    var focusHandler = function() {
      element.focus();
    };
    this._topFocusTrap.addEventListener('focus', focusHandler);
    this._bottomFocusTrap.addEventListener('focus', focusHandler);

    // Queue remove listeners function
    this._removeListeners.push(this._simpleBind(function() {
      this._topFocusTrap.removeEventListener('focus', focusHandler);
      this._bottomFocusTrap.removeEventListener('focus', focusHandler);
    }, this));

    // The top focus trap inserted immediately before the md-panel element (as
    // a sibling). The bottom focus trap inserted immediately after the
    // md-panel element (as a sibling).
    element[0].parentNode.insertBefore(this._topFocusTrap, element[0]);
    element.after(this._bottomFocusTrap);
  }
};


/**
 * Updates the animation of a panel.
 * @param {!MdPanelAnimation} animation
 */
MdPanelRef.prototype.updateAnimation = function(animation) {
  this.config['animation'] = animation;

  if (this._backdropRef) {
    this._backdropRef.config.animation.duration(animation._rawDuration);
  }
};


/**
 * Animate the panel opening.
 * @returns {!Q.IPromise} A promise that is resolved when the panel has
 *     animated open.
 * @private
 */
MdPanelRef.prototype._animateOpen = function() {
  this.panelContainer.addClass('md-panel-is-showing');
  var animationConfig = this.config['animation'];
  if (!animationConfig) {
    // Promise is in progress, return it.
    this.panelContainer.addClass('_md-panel-shown');
    return this._$q.when(this);
  }

  var self = this;
  return this._$q(function(resolve) {
    var done = self._done(resolve, self);
    var warnAndOpen = function() {
      self._$log.warn(
          'mdPanel: MdPanel Animations failed. ' +
          'Showing panel without animating.');
      done();
    };

    animationConfig.animateOpen(self.panelEl)
        .then(done, warnAndOpen);
  });
};


/**
 * Animate the panel closing.
 * @returns {!Q.IPromise} A promise that is resolved when the panel has animated closed.
 * @private
 */
MdPanelRef.prototype._animateClose = function() {
  var self = this;
  var animationConfig = this.config['animation'];

  if (!animationConfig) {
    this.panelContainer.removeClass('md-panel-is-showing');
    this.panelContainer.removeClass('_md-panel-shown');
    return this._$q.when(this);
  } else {
    return this._$q(function (resolve) {
      var done = function () {
        self.panelContainer.removeClass('md-panel-is-showing');
        // Remove the transform so that re-used panels don't accumulate transforms.
        self.panelEl.css('transform', '');
        resolve(self);
      };
      var warnAndClose = function () {
        self._$log.warn(
          'mdPanel: MdPanel Animations failed. Hiding panel without animating.');
        done();
      };

      animationConfig.animateClose(self.panelEl).then(done, warnAndClose);
    });
  }
};


/**
 * Registers a interceptor with the panel. The callback should return a promise,
 * which will allow the action to continue when it gets resolved, or will
 * prevent an action if it is rejected.
 * @param {string} type Type of interceptor.
 * @param {!Q.IPromise<!any>} callback Callback to be registered.
 * @returns {!MdPanelRef}
 */
MdPanelRef.prototype.registerInterceptor = function(type, callback) {
  var error = null;

  if (!angular.isString(type)) {
    error = 'Interceptor type must be a string, instead got ' + typeof type;
  } else if (!angular.isFunction(callback)) {
    error = 'Interceptor callback must be a function, instead got ' + typeof callback;
  }

  if (error) {
    throw new Error('MdPanel: ' + error);
  }

  var interceptors = this._interceptors[type] = this._interceptors[type] || [];

  if (interceptors.indexOf(callback) === -1) {
    interceptors.push(callback);
  }

  return this;
};


/**
 * Removes a registered interceptor.
 * @param {string} type Type of interceptor to be removed.
 * @param {Function} callback Interceptor to be removed.
 * @returns {!MdPanelRef}
 */
MdPanelRef.prototype.removeInterceptor = function(type, callback) {
  var index = this._interceptors[type] ?
    this._interceptors[type].indexOf(callback) : -1;

  if (index > -1) {
    this._interceptors[type].splice(index, 1);
  }

  return this;
};


/**
 * Removes all interceptors.
 * @param {string=} type Type of interceptors to be removed.
 *     If ommited, all interceptors types will be removed.
 * @returns {!MdPanelRef}
 */
MdPanelRef.prototype.removeAllInterceptors = function(type) {
  if (type) {
    this._interceptors[type] = [];
  } else {
    this._interceptors = Object.create(null);
  }

  return this;
};


/**
 * Invokes all the interceptors of a certain type sequantially in
 *     reverse order. Works in a similar way to `$q.all`, except it
 *     respects the order of the functions.
 * @param {string} type Type of interceptors to be invoked.
 * @returns {!Q.IPromise<!MdPanelRef>}
 * @private
 */
MdPanelRef.prototype._callInterceptors = function(type) {
  var self = this;
  var $q = self._$q;
  var interceptors = self._interceptors && self._interceptors[type] || [];

  return interceptors.reduceRight(function(promise, interceptor) {
    var isPromiseLike = interceptor && angular.isFunction(interceptor.then);
    var response = isPromiseLike ? interceptor : null;

    /**
    * For interceptors to reject/cancel subsequent portions of the chain, simply
    * return a `$q.reject(<value>)`
    */
    return promise.then(function() {
      if (!response) {
        try {
          response = interceptor(self);
        } catch (e) {
          response = $q.reject(e);
        }
      }

     return response;
    });
  }, $q.resolve(self));
};


/**
 * Faster, more basic than angular.bind
 * http://jsperf.com/angular-bind-vs-custom-vs-native
 * @param {function} callback
 * @param {!Object} self
 * @return {function} Callback function with a bound self.
 */
MdPanelRef.prototype._simpleBind = function(callback, self) {
  return function(value) {
    return callback.apply(self, value);
  };
};


/**
 * @param {function|IQResolveReject} callback
 * @param {!Object} self
 * @return {function} Callback function with a self param.
 */
MdPanelRef.prototype._done = function(callback, self) {
  return function() {
    callback(self);
  };
};


/**
 * Adds a panel to a group if the panel does not exist within the group already.
 * A panel can only exist within a single group.
 * @param {string} groupName The name of the group.
 */
MdPanelRef.prototype.addToGroup = function(groupName) {
  if (!this._$mdPanel._groups[groupName]) {
    this._$mdPanel.newPanelGroup(groupName);
  }

  var group = this._$mdPanel._groups[groupName];
  var index = group.panels.indexOf(this);

  if (index < 0) {
    group.panels.push(this);
  }
};


/**
 * Removes a panel from a group if the panel exists within that group. The group
 * must be created ahead of time.
 * @param {string} groupName The name of the group.
 */
MdPanelRef.prototype.removeFromGroup = function(groupName) {
  if (!this._$mdPanel._groups[groupName]) {
    throw new Error('mdPanel: The group ' + groupName + ' does not exist.');
  }

  var group = this._$mdPanel._groups[groupName];
  var index = group.panels.indexOf(this);

  if (index > -1) {
    group.panels.splice(index, 1);
  }
};


/**
 * Possible default closeReasons for the close function.
 * @enum {string}
 */
MdPanelRef.closeReasons = {
  CLICK_OUTSIDE: 'clickOutsideToClose',
  ESCAPE: 'escapeToClose',
};


/*****************************************************************************
 *                               MdPanelPosition                             *
 *****************************************************************************/


/**
 * Position configuration object. To use, create an MdPanelPosition with the
 * desired properties, then pass the object as part of $mdPanel creation.
 *
 * Example:
 *
 * var panelPosition = new MdPanelPosition()
 *     .relativeTo(myButtonEl)
 *     .addPanelPosition(
 *       $mdPanel.xPosition.CENTER,
 *       $mdPanel.yPosition.ALIGN_TOPS
 *     );
 *
 * $mdPanel.create({
 *   position: panelPosition
 * });
 *
 * @param {!IInjectorService} $injector
 * @final @constructor
 */
function MdPanelPosition($injector) {
  /** @private @const {!IWindowService} */
  this._$window = $injector.get('$window');

  /** @private {boolean} */
  this._isRTL = $injector.get('$mdUtil').isRtl();

  /** @private @const {!angular.$mdConstant} */
  this._$mdConstant = $injector.get('$mdConstant');

  /** @private {boolean} */
  this._absolute = false;

  /** @private {!JQLite} */
  this._relativeToEl = undefined;

  /** @private {string} */
  this._top = '';

  /** @private {string} */
  this._bottom = '';

  /** @private {string} */
  this._left = '';

  /** @private {string} */
  this._right = '';

  /** @private {!Array<string>} */
  this._translateX = [];

  /** @private {!Array<string>} */
  this._translateY = [];

  /** @private {!Array<{x:string, y:string}>} */
  this._positions = [];

  /** @private {?{x:string, y:string}} */
  this._actualPosition = undefined;
}


/**
 * Possible values of xPosition.
 * @enum {string}
 */
MdPanelPosition.xPosition = {
  CENTER: 'center',
  ALIGN_START: 'align-start',
  ALIGN_END: 'align-end',
  OFFSET_START: 'offset-start',
  OFFSET_END: 'offset-end'
};


/**
 * Possible values of yPosition.
 * @enum {string}
 */
MdPanelPosition.yPosition = {
  CENTER: 'center',
  ALIGN_TOPS: 'align-tops',
  ALIGN_BOTTOMS: 'align-bottoms',
  ABOVE: 'above',
  BELOW: 'below'
};


/**
 * Possible values of absolute position.
 * @enum {string}
 */
MdPanelPosition.absPosition = {
  TOP: 'top',
  RIGHT: 'right',
  BOTTOM: 'bottom',
  LEFT: 'left'
};

/**
 * Margin between the edges of a panel and the viewport.
 * @const {number}
 */
MdPanelPosition.viewportMargin = 8;


/**
 * Sets absolute positioning for the panel.
 * @return {!MdPanelPosition}
 */
MdPanelPosition.prototype.absolute = function() {
  this._absolute = true;
  return this;
};


/**
 * Sets the value of a position for the panel. Clears any previously set
 * position.
 * @param {string} position Position to set
 * @param {string=} value Value of the position. Defaults to '0'.
 * @returns {!MdPanelPosition}
 * @private
 */
MdPanelPosition.prototype._setPosition = function(position, value) {
  if (position === MdPanelPosition.absPosition.RIGHT ||
      position === MdPanelPosition.absPosition.LEFT) {
    this._left = this._right = '';
  } else if (
      position === MdPanelPosition.absPosition.BOTTOM ||
      position === MdPanelPosition.absPosition.TOP) {
    this._top = this._bottom = '';
  } else {
    var positions = Object.keys(MdPanelPosition.absPosition).join()
        .toLowerCase();

    throw new Error('mdPanel: Position must be one of ' + positions + '.');
  }

  this['_' +  position] = angular.isString(value) ? value : '0';

  return this;
};


/**
 * Sets the value of `top` for the panel. Clears any previously set vertical
 * position.
 * @param {string=} top Value of `top`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.top = function(top) {
  return this._setPosition(MdPanelPosition.absPosition.TOP, top);
};


/**
 * Sets the value of `bottom` for the panel. Clears any previously set vertical
 * position.
 * @param {string=} bottom Value of `bottom`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.bottom = function(bottom) {
  return this._setPosition(MdPanelPosition.absPosition.BOTTOM, bottom);
};


/**
 * Sets the panel to the start of the page - `left` if `ltr` or `right` for
 * `rtl`. Clears any previously set horizontal position.
 * @param {string=} start Value of position. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.start = function(start) {
  var position = this._isRTL ? MdPanelPosition.absPosition.RIGHT : MdPanelPosition.absPosition.LEFT;
  return this._setPosition(position, start);
};


/**
 * Sets the panel to the end of the page - `right` if `ltr` or `left` for `rtl`.
 * Clears any previously set horizontal position.
 * @param {string=} end Value of position. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.end = function(end) {
  var position = this._isRTL ? MdPanelPosition.absPosition.LEFT : MdPanelPosition.absPosition.RIGHT;
  return this._setPosition(position, end);
};


/**
 * Sets the value of `left` for the panel. Clears any previously set
 * horizontal position.
 * @param {string=} left Value of `left`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.left = function(left) {
  return this._setPosition(MdPanelPosition.absPosition.LEFT, left);
};


/**
 * Sets the value of `right` for the panel. Clears any previously set
 * horizontal position.
 * @param {string=} right Value of `right`. Defaults to '0'.
 * @returns {!MdPanelPosition}
*/
MdPanelPosition.prototype.right = function(right) {
  return this._setPosition(MdPanelPosition.absPosition.RIGHT, right);
};


/**
 * Centers the panel horizontally in the viewport. Clears any previously set
 * horizontal position.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.centerHorizontally = function() {
  this._left = '50%';
  this._right = '';
  this._translateX = ['-50%'];
  return this;
};


/**
 * Centers the panel vertically in the viewport. Clears any previously set
 * vertical position.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.centerVertically = function() {
  this._top = '50%';
  this._bottom = '';
  this._translateY = ['-50%'];
  return this;
};


/**
 * Centers the panel horizontally and vertically in the viewport. This is
 * equivalent to calling both `centerHorizontally` and `centerVertically`.
 * Clears any previously set horizontal and vertical positions.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.center = function() {
  return this.centerHorizontally().centerVertically();
};


/**
 * Sets element for relative positioning.
 * @param {string|!Element|!JQLite} element Query selector, DOM element,
 *     or angular element to set the panel relative to.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.relativeTo = function(element) {
  this._absolute = false;
  this._relativeToEl = getElement(element);
  return this;
};


/**
 * Sets the x and y positions for the panel relative to another element.
 * @param {string} xPosition must be one of the MdPanelPosition.xPosition
 *     values.
 * @param {string} yPosition must be one of the MdPanelPosition.yPosition
 *     values.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.addPanelPosition = function(xPosition, yPosition) {
  if (!this._relativeToEl) {
    throw new Error('mdPanel: addPanelPosition can only be used with ' +
        'relative positioning. Set relativeTo first.');
  }

  validatePosition(MdPanelPosition.xPosition, xPosition);
  validatePosition(MdPanelPosition.yPosition, yPosition);

  this._positions.push({
    x: xPosition,
    y: yPosition
  });

  return this;
};


/**
 * Sets the value of the offset in the x-direction. This will add to any
 * previously set offsets.
 * @param {string|number|function(MdPanelPosition): string} offsetX
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.withOffsetX = function(offsetX) {
  this._translateX.push(addUnits(offsetX));
  return this;
};


/**
 * Sets the value of the offset in the y-direction. This will add to any
 * previously set offsets.
 * @param {string|number|function(MdPanelPosition): string} offsetY
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.withOffsetY = function(offsetY) {
  this._translateY.push(addUnits(offsetY));
  return this;
};


/**
 * Gets the value of `top` for the panel.
 * @returns {string}
 */
MdPanelPosition.prototype.getTop = function() {
  return this._top;
};


/**
 * Gets the value of `bottom` for the panel.
 * @returns {string}
 */
MdPanelPosition.prototype.getBottom = function() {
  return this._bottom;
};


/**
 * Gets the value of `left` for the panel.
 * @returns {string}
 */
MdPanelPosition.prototype.getLeft = function() {
  return this._left;
};


/**
 * Gets the value of `right` for the panel.
 * @returns {string}
 */
MdPanelPosition.prototype.getRight = function() {
  return this._right;
};


/**
 * Gets the value of `transform` for the panel.
 * @returns {string} representation of the translateX and translateY rules and values
 */
MdPanelPosition.prototype.getTransform = function() {
  var translateX = this._reduceTranslateValues('translateX', this._translateX);
  var translateY = this._reduceTranslateValues('translateY', this._translateY);

  // It's important to trim the result, because the browser will ignore the set
  // operation if the string contains only whitespace.
  return (translateX + ' ' + translateY).trim();
};


/**
 * Sets the `transform` value for an element.
 * @param {!JQLite} el
 * @returns {!JQLite}
 * @private
 */
MdPanelPosition.prototype._setTransform = function(el) {
  return el.css(this._$mdConstant.CSS.TRANSFORM, this.getTransform());
};


/**
 * True if the panel is completely on-screen with this positioning; false
 * otherwise.
 * @param {!JQLite} el
 * @return {boolean}
 * @private
 */
MdPanelPosition.prototype._isOnscreen = function(el) {
  // this works because we always use fixed positioning for the panel,
  // which is relative to the viewport.
  var left = parseInt(this.getLeft());
  var top = parseInt(this.getTop());

  if (this._translateX.length || this._translateY.length) {
    var prefixedTransform = this._$mdConstant.CSS.TRANSFORM;
    var offsets = getComputedTranslations(el, prefixedTransform);
    left += offsets.x;
    top += offsets.y;
  }

  var right = left + el[0].offsetWidth;
  var bottom = top + el[0].offsetHeight;

  return (left >= 0) &&
    (top >= 0) &&
    (bottom <= this._$window.innerHeight) &&
    (right <= this._$window.innerWidth);
};


/**
 * Gets the first x/y position that can fit on-screen.
 * @returns {{x: string, y: string}}
 */
MdPanelPosition.prototype.getActualPosition = function() {
  return this._actualPosition;
};


/**
 * Reduces a list of translate values to a string that can be used within
 * transform.
 * @param {string} translateFn
 * @param {!Array<string>} values
 * @returns {string}
 * @private
 */
MdPanelPosition.prototype._reduceTranslateValues =
    function(translateFn, values) {
      return values.map(function(translation) {
        var translationValue = angular.isFunction(translation) ?
            addUnits(translation(this)) : translation;
        return translateFn + '(' + translationValue + ')';
      }, this).join(' ');
    };


/**
 * Sets the panel position based on the created panel element and best x/y
 * positioning.
 * @param {!JQLite} el
 * @private
 */
MdPanelPosition.prototype._setPanelPosition = function(el) {
  // Remove the class in case it has been added before.
  el.removeClass('_md-panel-position-adjusted');

  // Only calculate the position if necessary.
  if (this._absolute) {
    this._setTransform(el);
    return;
  }

  if (this._actualPosition) {
    this._calculatePanelPosition(el, this._actualPosition);
    this._setTransform(el);
    this._constrainToViewport(el);
    return;
  }

  for (var i = 0; i < this._positions.length; i++) {
    this._actualPosition = this._positions[i];
    this._calculatePanelPosition(el, this._actualPosition);
    this._setTransform(el);

    if (this._isOnscreen(el)) {
      return;
    }
  }

  this._constrainToViewport(el);
};


/**
 * Constrains a panel's position to the viewport.
 * @param {!JQLite} el
 * @private
 */
MdPanelPosition.prototype._constrainToViewport = function(el) {
  var margin = MdPanelPosition.viewportMargin;
  var initialTop = this._top;
  var initialLeft = this._left;

  if (this.getTop()) {
    var top = parseInt(this.getTop());
    var bottom = el[0].offsetHeight + top;
    var viewportHeight = this._$window.innerHeight;

    if (top < margin) {
      this._top = margin + 'px';
    } else if (bottom > viewportHeight) {
      this._top = top - (bottom - viewportHeight + margin) + 'px';
    }
  }

  if (this.getLeft()) {
    var left = parseInt(this.getLeft());
    var right = el[0].offsetWidth + left;
    var viewportWidth = this._$window.innerWidth;

    if (left < margin) {
      this._left = margin + 'px';
    } else if (right > viewportWidth) {
      this._left = left - (right - viewportWidth + margin) + 'px';
    }
  }

  // Class that can be used to re-style the panel if it was repositioned.
  el.toggleClass(
    '_md-panel-position-adjusted',
    this._top !== initialTop || this._left !== initialLeft
  );
};


/**
 * Switches between 'start' and 'end'.
 * @param {string} position Horizontal position of the panel
 * @returns {string} Reversed position
 * @private
 */
MdPanelPosition.prototype._reverseXPosition = function(position) {
  if (position === MdPanelPosition.xPosition.CENTER) {
    return position;
  }

  var start = 'start';
  var end = 'end';

  return position.indexOf(start) > -1 ? position.replace(start, end) : position.replace(end, start);
};


/**
 * Handles horizontal positioning in rtl or ltr environments.
 * @param {string} position Horizontal position of the panel
 * @returns {string} The correct position according the page direction
 * @private
 */
MdPanelPosition.prototype._bidi = function(position) {
  return this._isRTL ? this._reverseXPosition(position) : position;
};


/**
 * Calculates the panel position based on the created panel element and the
 * provided positioning.
 * @param {!JQLite} el
 * @param {!{x:string, y:string}} position
 * @private
 */
MdPanelPosition.prototype._calculatePanelPosition = function(el, position) {

  var panelBounds = el[0].getBoundingClientRect();
  var panelWidth = Math.max(panelBounds.width, el[0].clientWidth);
  var panelHeight = Math.max(panelBounds.height, el[0].clientHeight);

  var targetBounds = this._relativeToEl[0].getBoundingClientRect();

  var targetLeft = targetBounds.left;
  var targetRight = targetBounds.right;
  var targetWidth = targetBounds.width;

  switch (this._bidi(position.x)) {
    case MdPanelPosition.xPosition.OFFSET_START:
      this._left = targetLeft - panelWidth + 'px';
      break;
    case MdPanelPosition.xPosition.ALIGN_END:
      this._left = targetRight - panelWidth + 'px';
      break;
    case MdPanelPosition.xPosition.CENTER:
      var left = targetLeft + (0.5 * targetWidth) - (0.5 * panelWidth);
      this._left = left + 'px';
      break;
    case MdPanelPosition.xPosition.ALIGN_START:
      this._left = targetLeft + 'px';
      break;
    case MdPanelPosition.xPosition.OFFSET_END:
      this._left = targetRight + 'px';
      break;
  }

  var targetTop = targetBounds.top;
  var targetBottom = targetBounds.bottom;
  var targetHeight = targetBounds.height;

  switch (position.y) {
    case MdPanelPosition.yPosition.ABOVE:
      this._top = targetTop - panelHeight + 'px';
      break;
    case MdPanelPosition.yPosition.ALIGN_BOTTOMS:
      this._top = targetBottom - panelHeight + 'px';
      break;
    case MdPanelPosition.yPosition.CENTER:
      var top = targetTop + (0.5 * targetHeight) - (0.5 * panelHeight);
      this._top = top + 'px';
      break;
    case MdPanelPosition.yPosition.ALIGN_TOPS:
      this._top = targetTop + 'px';
      break;
    case MdPanelPosition.yPosition.BELOW:
      this._top = targetBottom + 'px';
      break;
  }
};


/*****************************************************************************
 *                               MdPanelAnimation                            *
 *****************************************************************************/


/**
 * Animation configuration object. To use, create an MdPanelAnimation with the
 * desired properties, then pass the object as part of $mdPanel creation.
 *
 * Example:
 *
 * var panelAnimation = new MdPanelAnimation()
 *     .openFrom(myButtonEl)
 *     .closeTo('.my-button')
 *     .withAnimation($mdPanel.animation.SCALE);
 *
 * $mdPanel.create({
 *   animation: panelAnimation
 * });
 *
 * @param {!IInjectorService} $injector
 * @final @constructor
 */
function MdPanelAnimation($injector) {
  /** @private @const {!angular.$mdUtil} */
  this._$mdUtil = $injector.get('$mdUtil');

  /**
   * @private {{element: !JQLite|undefined, bounds: !DOMRect}|
   *     undefined}
   */
  this._openFrom;

  /**
   * @private {{element: !JQLite|undefined, bounds: !DOMRect}|
   *     undefined}
   */
  this._closeTo;

  /** @private {string|{open: string, close: string}} */
  this._animationClass = '';

  /** @private {number} */
  this._openDuration;

  /** @private {number} */
  this._closeDuration;

  /** @private {number|{open: number, close: number}} */
  this._rawDuration;
}


/**
 * Possible default animations.
 * @enum {string}
 */
MdPanelAnimation.animation = {
  SLIDE: 'md-panel-animate-slide',
  SCALE: 'md-panel-animate-scale',
  FADE: 'md-panel-animate-fade'
};


/**
 * Specifies where to start the open animation. `openFrom` accepts a
 * click event object, query selector, DOM element, or a Rect object that
 * is used to determine the bounds. When passed a click event, the location
 * of the click will be used as the position to start the animation.
 * @param {string|!Element|!Event|{top: number, left: number}} openFrom
 * @returns {!MdPanelAnimation}
 */
MdPanelAnimation.prototype.openFrom = function(openFrom) {
  // Check if 'openFrom' is an Event.
  openFrom = openFrom.target ? openFrom.target : openFrom;

  this._openFrom = this._getPanelAnimationTarget(openFrom);

  if (!this._closeTo) {
    this._closeTo = this._openFrom;
  }
  return this;
};


/**
 * Specifies where to animate the panel close. `closeTo` accepts a
 * query selector, DOM element, or a Rect object that is used to determine
 * the bounds.
 * @param {string|!Element|{top: number, left: number}} closeTo
 * @returns {!MdPanelAnimation}
 */
MdPanelAnimation.prototype.closeTo = function(closeTo) {
  this._closeTo = this._getPanelAnimationTarget(closeTo);
  return this;
};


/**
 * Specifies the duration of the animation in milliseconds.
 * @param {number|{open: number, close: number}} duration
 * @returns {!MdPanelAnimation}
 */
MdPanelAnimation.prototype.duration = function(duration) {
  if (duration) {
    if (angular.isNumber(duration)) {
      this._openDuration = this._closeDuration = toSeconds(duration);
    } else if (angular.isObject(duration)) {
      this._openDuration = toSeconds(duration.open);
      this._closeDuration = toSeconds(duration.close);
    }
  }

  // Save the original value so it can be passed to the backdrop.
  this._rawDuration = duration;

  return this;

  function toSeconds(value) {
    if (angular.isNumber(value)) return value / 1000;
  }
};


/**
 * Returns the element and bounds for the animation target.
 * @param {string|!Element|{top: number, left: number}} location
 * @returns {{element: !JQLite|undefined, bounds: !DOMRect}}
 * @private
 */
MdPanelAnimation.prototype._getPanelAnimationTarget = function(location) {
  if (angular.isDefined(location.top) || angular.isDefined(location.left)) {
    return {
      element: undefined,
      bounds: {
        top: location.top || 0,
        left: location.left || 0
      }
    };
  } else {
    return this._getBoundingClientRect(getElement(location));
  }
};


/**
 * Specifies the animation class.
 *
 * There are several default animations that can be used:
 * (MdPanelAnimation.animation)
 *   SLIDE: The panel slides in and out from the specified
 *        elements.
 *   SCALE: The panel scales in and out.
 *   FADE: The panel fades in and out.
 *
 * @param {string|{open: string, close: string}} cssClass
 * @returns {!MdPanelAnimation}
 */
MdPanelAnimation.prototype.withAnimation = function(cssClass) {
  this._animationClass = cssClass;
  return this;
};


/**
 * Animate the panel open.
 * @param {!JQLite} panelEl
 * @returns {!Q.IPromise} A promise that is resolved when the open
 *     animation is complete.
 */
MdPanelAnimation.prototype.animateOpen = function(panelEl) {
  var animator = this._$mdUtil.dom.animator;

  this._fixBounds(panelEl);
  var animationOptions = {};

  // Include the panel transformations when calculating the animations.
  var panelTransform = panelEl[0].style.transform || '';

  var openFrom = animator.toTransformCss(panelTransform);
  var openTo = animator.toTransformCss(panelTransform);

  switch (this._animationClass) {
    case MdPanelAnimation.animation.SLIDE:
      // Slide should start with opacity: 1.
      panelEl.css('opacity', '1');

      animationOptions = {
        transitionInClass: '_md-panel-animate-enter',
        transitionOutClass: '_md-panel-animate-leave',
      };

      var openSlide = animator.calculateSlideToOrigin(
              panelEl, this._openFrom) || '';
      openFrom = animator.toTransformCss(openSlide + ' ' + panelTransform);
      break;

    case MdPanelAnimation.animation.SCALE:
      animationOptions = {
        transitionInClass: '_md-panel-animate-enter'
      };

      var openScale = animator.calculateZoomToOrigin(
              panelEl, this._openFrom) || '';
      openFrom = animator.toTransformCss(panelTransform + ' ' + openScale);
      break;

    case MdPanelAnimation.animation.FADE:
      animationOptions = {
        transitionInClass: '_md-panel-animate-enter'
      };
      break;

    default:
      if (angular.isString(this._animationClass)) {
        animationOptions = {
          transitionInClass: this._animationClass
        };
      } else {
        animationOptions = {
          transitionInClass: this._animationClass['open'],
          transitionOutClass: this._animationClass['close'],
        };
      }
  }

  animationOptions.duration = this._openDuration;

  return animator
      .translate3d(panelEl, openFrom, openTo, animationOptions);
};


/**
 * Animate the panel close.
 * @param {!JQLite} panelEl
 * @returns {!Q.IPromise} A promise that resolves when the close animation is complete.
 */
MdPanelAnimation.prototype.animateClose = function(panelEl) {
  var animator = this._$mdUtil.dom.animator;
  var reverseAnimationOptions = {};

  // Include the panel transformations when calculating the animations.
  var panelTransform = panelEl[0].style.transform || '';

  var closeFrom = animator.toTransformCss(panelTransform);
  var closeTo = animator.toTransformCss(panelTransform);

  switch (this._animationClass) {
    case MdPanelAnimation.animation.SLIDE:
      // Slide should start with opacity: 1.
      panelEl.css('opacity', '1');
      reverseAnimationOptions = {
        transitionInClass: '_md-panel-animate-leave',
        transitionOutClass: '_md-panel-animate-enter _md-panel-animate-leave'
      };

      var closeSlide = animator.calculateSlideToOrigin(panelEl, this._closeTo) || '';
      closeTo = animator.toTransformCss(closeSlide + ' ' + panelTransform);
      break;

    case MdPanelAnimation.animation.SCALE:
      reverseAnimationOptions = {
        transitionInClass: '_md-panel-animate-scale-out _md-panel-animate-leave',
        transitionOutClass: '_md-panel-animate-scale-out _md-panel-animate-enter _md-panel-animate-leave'
      };

      var closeScale = animator.calculateZoomToOrigin(panelEl, this._closeTo) || '';
      closeTo = animator.toTransformCss(panelTransform + ' ' + closeScale);
      break;

    case MdPanelAnimation.animation.FADE:
      reverseAnimationOptions = {
        transitionInClass: '_md-panel-animate-fade-out _md-panel-animate-leave',
        transitionOutClass: '_md-panel-animate-fade-out _md-panel-animate-enter _md-panel-animate-leave'
      };
      break;

    default:
      if (angular.isString(this._animationClass)) {
        reverseAnimationOptions = {
          transitionOutClass: this._animationClass
        };
      } else {
        reverseAnimationOptions = {
          transitionInClass: this._animationClass['close'],
          transitionOutClass: this._animationClass['open']
        };
      }
  }

  reverseAnimationOptions.duration = this._closeDuration;

  return animator
      .translate3d(panelEl, closeFrom, closeTo, reverseAnimationOptions);
};


/**
 * Set the height and width to match the panel if not provided.
 * @param {!JQLite} panelEl
 * @private
 */
MdPanelAnimation.prototype._fixBounds = function(panelEl) {
  var panelWidth = panelEl[0].offsetWidth;
  var panelHeight = panelEl[0].offsetHeight;

  if (this._openFrom && this._openFrom.bounds.height == null) {
    this._openFrom.bounds.height = panelHeight;
  }
  if (this._openFrom && this._openFrom.bounds.width == null) {
    this._openFrom.bounds.width = panelWidth;
  }
  if (this._closeTo && this._closeTo.bounds.height == null) {
    this._closeTo.bounds.height = panelHeight;
  }
  if (this._closeTo && this._closeTo.bounds.width == null) {
    this._closeTo.bounds.width = panelWidth;
  }
};


/**
 * Identify the bounding RECT for the target element.
 * @param {!JQLite} element
 * @returns {{element: !JQLite|undefined, bounds: !DOMRect}}
 * @private
 */
MdPanelAnimation.prototype._getBoundingClientRect = function(element) {
  if (element instanceof angular.element) {
    return {
      element: element,
      bounds: element[0].getBoundingClientRect()
    };
  }
};


/*****************************************************************************
 *                                Util Methods                               *
 *****************************************************************************/


/**
 * Returns the angular element associated with a css selector or element.
 * @param el {string|!JQLite|!Element}
 * @returns {!JQLite}
 */
function getElement(el) {
  var queryResult = angular.isString(el) ?
      document.querySelector(el) : el;
  return angular.element(queryResult);
}

/**
 * Gets the computed values for an element's translateX and translateY in px.
 * @param {!JQLite|!Element} el the element to evaluate
 * @param {string} property
 * @return {{x: number, y: number}} an element's translateX and translateY in px
 */
function getComputedTranslations(el, property) {
  // The transform being returned by `getComputedStyle` is in the format:
  // `matrix(a, b, c, d, translateX, translateY)` if defined and `none`
  // if the element doesn't have a transform.
  var transform = getComputedStyle(el[0] || el)[property];
  var openIndex = transform.indexOf('(');
  var closeIndex = transform.lastIndexOf(')');
  var output = { x: 0, y: 0 };

  if (openIndex > -1 && closeIndex > -1) {
    var parsedValues = transform
      .substring(openIndex + 1, closeIndex)
      .split(', ')
      .slice(-2);

    output.x = parseInt(parsedValues[0]);
    output.y = parseInt(parsedValues[1]);
  }

  return output;
}

/*
 * Ensures that a value is a valid position name. Throw an exception if not.
 * @param {Object} positionMap Object against which the value will be checked.
 * @param {string} value
 */
function validatePosition(positionMap, value) {
  // empty is ok
  if (value === null || angular.isUndefined(value)) {
    return;
  }

  var positionKeys = Object.keys(positionMap);
  var positionValues = [];

  for (var key, i = 0; key = positionKeys[i]; i++) {
    var position = positionMap[key];
    positionValues.push(position);

    if (position === value) {
      return;
    }
  }

  throw new Error('Panel position only accepts the following values:\n' +
    positionValues.join(' | '));
}

/**
 * Adds units to a number value.
 * @param {string|number} value
 * @return {string}
 */
function addUnits(value) {
  return angular.isNumber(value) ? value + 'px' : value;
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.progressCircular
 * @description Module for a circular progressbar
 */

angular.module('material.components.progressCircular', ['material.core']);

})();
(function(){
"use strict";

/**
 * @ngdoc directive
 * @name mdProgressCircular
 * @module material.components.progressCircular
 * @restrict E
 *
 * @description
 * The circular progress directive is used to make loading content in your app as delightful and
 * painless as possible by minimizing the amount of visual change a user sees before they can view
 * and interact with content.
 *
 * For operations where the percentage of the operation completed can be determined, use a
 * determinate indicator. They give users a quick sense of how long an operation will take.
 *
 * For operations where the user is asked to wait a moment while something finishes up, and it’s
 * not necessary to expose what's happening behind the scenes and how long it will take, use an
 * indeterminate indicator.
 *
 * @param {string} md-mode Select from one of two modes: **'determinate'** and **'indeterminate'**.
 *
 * Note: if the `md-mode` value is set as undefined or specified as not 1 of the two (2) valid modes, then **'indeterminate'**
 * will be auto-applied as the mode.
 *
 * Note: if not configured, the `md-mode="indeterminate"` will be auto injected as an attribute.
 * If `value=""` is also specified, however, then `md-mode="determinate"` would be auto-injected instead.
 * @param {number=} value In determinate mode, this number represents the percentage of the
 *     circular progress. Default: 0
 * @param {number=} md-diameter This specifies the diameter of the circular progress. The value
 * should be a pixel-size value (eg '100'). If this attribute is
 * not present then a default value of '50px' is assumed.
 *
 * @param {boolean=} ng-disabled Determines whether to disable the progress element.
 *
 * @usage
 * <hljs lang="html">
 * <md-progress-circular md-mode="determinate" value="..."></md-progress-circular>
 *
 * <md-progress-circular md-mode="determinate" ng-value="..."></md-progress-circular>
 *
 * <md-progress-circular md-mode="determinate" value="..." md-diameter="100"></md-progress-circular>
 *
 * <md-progress-circular md-mode="indeterminate"></md-progress-circular>
 * </hljs>
 */

MdProgressCircularDirective.$inject = ["$window", "$mdProgressCircular", "$mdTheming", "$mdUtil", "$interval", "$log"];
angular
  .module('material.components.progressCircular')
  .directive('mdProgressCircular', MdProgressCircularDirective);

/* @ngInject */
function MdProgressCircularDirective($window, $mdProgressCircular, $mdTheming,
                                     $mdUtil, $interval, $log) {

  // Note that this shouldn't use use $$rAF, because it can cause an infinite loop
  // in any tests that call $animate.flush.
  var rAF = $window.requestAnimationFrame ||
            $window.webkitRequestAnimationFrame ||
            angular.noop;

  var cAF = $window.cancelAnimationFrame ||
            $window.webkitCancelAnimationFrame ||
            $window.webkitCancelRequestAnimationFrame ||
            angular.noop;

  var MODE_DETERMINATE = 'determinate';
  var MODE_INDETERMINATE = 'indeterminate';
  var DISABLED_CLASS = '_md-progress-circular-disabled';
  var INDETERMINATE_CLASS = 'md-mode-indeterminate';

  return {
    restrict: 'E',
    scope: {
      value: '@',
      mdDiameter: '@',
      mdMode: '@'
    },
    template:
      '<svg xmlns="http://www.w3.org/2000/svg">' +
        '<path fill="none"/>' +
      '</svg>',
    compile: function(element, attrs) {
      element.attr({
        'aria-valuemin': 0,
        'aria-valuemax': 100,
        'role': 'progressbar'
      });

      if (angular.isUndefined(attrs.mdMode)) {
        var mode = attrs.hasOwnProperty('value') ? MODE_DETERMINATE : MODE_INDETERMINATE;
        attrs.$set('mdMode', mode);
      } else {
        attrs.$set('mdMode', attrs.mdMode.trim());
      }

      return MdProgressCircularLink;
    }
  };

  function MdProgressCircularLink(scope, element, attrs) {
    var node = element[0];
    var svg = angular.element(node.querySelector('svg'));
    var path = angular.element(node.querySelector('path'));
    var startIndeterminate = $mdProgressCircular.startIndeterminate;
    var endIndeterminate = $mdProgressCircular.endIndeterminate;
    var iterationCount = 0;
    var lastAnimationId = 0;
    var lastDrawFrame;
    var interval;

    $mdTheming(element);
    element.toggleClass(DISABLED_CLASS, attrs.hasOwnProperty('disabled'));

    // If the mode is indeterminate, it doesn't need to
    // wait for the next digest. It can start right away.
    if (scope.mdMode === MODE_INDETERMINATE){
      startIndeterminateAnimation();
    }

    scope.$on('$destroy', function(){
      cleanupIndeterminateAnimation();

      if (lastDrawFrame) {
        cAF(lastDrawFrame);
      }
    });

    scope.$watchGroup(['value', 'mdMode', function() {
      var isDisabled = node.disabled;

      // Sometimes the browser doesn't return a boolean, in
      // which case we should check whether the attribute is
      // present.
      if (isDisabled === true || isDisabled === false){
        return isDisabled;
      }

      return angular.isDefined(element.attr('disabled'));
    }], function(newValues, oldValues) {
      var mode = newValues[1];
      var isDisabled = newValues[2];
      var wasDisabled = oldValues[2];
      var diameter = 0;
      var strokeWidth = 0;

      if (isDisabled !== wasDisabled) {
        element.toggleClass(DISABLED_CLASS, !!isDisabled);
      }

      if (isDisabled) {
        cleanupIndeterminateAnimation();
      } else {
        if (mode !== MODE_DETERMINATE && mode !== MODE_INDETERMINATE) {
          mode = MODE_INDETERMINATE;
          attrs.$set('mdMode', mode);
        }

        if (mode === MODE_INDETERMINATE) {
          if (oldValues[1] === MODE_DETERMINATE) {
            diameter = getSize(scope.mdDiameter);
            strokeWidth = getStroke(diameter);
            path.attr('d', getSvgArc(diameter, strokeWidth, true));
            path.attr('stroke-dasharray', getDashLength(diameter, strokeWidth, 75));
          }
          startIndeterminateAnimation();
        } else {
          var newValue = clamp(newValues[0]);
          var oldValue = clamp(oldValues[0]);

          cleanupIndeterminateAnimation();

          if (oldValues[1] === MODE_INDETERMINATE) {
            diameter = getSize(scope.mdDiameter);
            strokeWidth = getStroke(diameter);
            path.attr('d', getSvgArc(diameter, strokeWidth, false));
            path.attr('stroke-dasharray', getDashLength(diameter, strokeWidth, 100));
          }

          element.attr('aria-valuenow', newValue);
          renderCircle(oldValue, newValue);
        }
      }

    });

    // This is in a separate watch in order to avoid layout, unless
    // the value has actually changed.
    scope.$watch('mdDiameter', function(newValue) {
      var diameter = getSize(newValue);
      var strokeWidth = getStroke(diameter);
      var value = clamp(scope.value);
      var transformOrigin = (diameter / 2) + 'px';
      var dimensions = {
        width: diameter + 'px',
        height: diameter + 'px'
      };

      // The viewBox has to be applied via setAttribute, because it is
      // case-sensitive. If jQuery is included in the page, `.attr` lowercases
      // all attribute names.
      svg[0].setAttribute('viewBox', '0 0 ' + diameter + ' ' + diameter);

      // Usually viewBox sets the dimensions for the SVG, however that doesn't
      // seem to be the case on IE10.
      // Important! The transform origin has to be set from here and it has to
      // be in the format of "Ypx Ypx Ypx", otherwise the rotation wobbles in
      // IE and Edge, because they don't account for the stroke width when
      // rotating. Also "center" doesn't help in this case, it has to be a
      // precise value.
      svg
        .css(dimensions)
        .css('transform-origin', transformOrigin + ' ' + transformOrigin + ' ' + transformOrigin);

      element.css(dimensions);

      path.attr('stroke-width', strokeWidth);
      path.attr('stroke-linecap', 'square');
      if (scope.mdMode == MODE_INDETERMINATE) {
        path.attr('d', getSvgArc(diameter, strokeWidth, true));
        path.attr('stroke-dasharray', getDashLength(diameter, strokeWidth, 75));
        path.attr('stroke-dashoffset', getDashOffset(diameter, strokeWidth, 1, 75));
      } else {
        path.attr('d', getSvgArc(diameter, strokeWidth, false));
        path.attr('stroke-dasharray', getDashLength(diameter, strokeWidth, 100));
        path.attr('stroke-dashoffset', getDashOffset(diameter, strokeWidth, 0, 100));
        renderCircle(value, value);
      }

    });

    function renderCircle(animateFrom, animateTo, easing, duration, iterationCount, maxValue) {
      var id = ++lastAnimationId;
      var startTime = $mdUtil.now();
      var changeInValue = animateTo - animateFrom;
      var diameter = getSize(scope.mdDiameter);
      var strokeWidth = getStroke(diameter);
      var ease = easing || $mdProgressCircular.easeFn;
      var animationDuration = duration || $mdProgressCircular.duration;
      var rotation = -90 * (iterationCount || 0);
      var dashLimit = maxValue || 100;

      // No need to animate it if the values are the same
      if (animateTo === animateFrom) {
        renderFrame(animateTo);
      } else {
        lastDrawFrame = rAF(function animation() {
          var currentTime = $window.Math.max(0, $window.Math.min($mdUtil.now() - startTime, animationDuration));

          renderFrame(ease(currentTime, animateFrom, changeInValue, animationDuration));

          // Do not allow overlapping animations
          if (id === lastAnimationId && currentTime < animationDuration) {
            lastDrawFrame = rAF(animation);
          }
        });
      }

      function renderFrame(value) {
        path.attr('stroke-dashoffset', getDashOffset(diameter, strokeWidth, value, dashLimit));
        path.attr('transform','rotate(' + (rotation) + ' ' + diameter/2 + ' ' + diameter/2 + ')');
      }
    }

    function animateIndeterminate() {
      renderCircle(
        startIndeterminate,
        endIndeterminate,
        $mdProgressCircular.easeFnIndeterminate,
        $mdProgressCircular.durationIndeterminate,
        iterationCount,
        75
      );

      // The %4 technically isn't necessary, but it keeps the rotation
      // under 360, instead of becoming a crazy large number.
      iterationCount = ++iterationCount % 4;

    }

    function startIndeterminateAnimation() {
      if (!interval) {
        // Note that this interval isn't supposed to trigger a digest.
        interval = $interval(
          animateIndeterminate,
          $mdProgressCircular.durationIndeterminate,
          0,
          false
        );

        animateIndeterminate();

        element
          .addClass(INDETERMINATE_CLASS)
          .removeAttr('aria-valuenow');
      }
    }

    function cleanupIndeterminateAnimation() {
      if (interval) {
        $interval.cancel(interval);
        interval = null;
        element.removeClass(INDETERMINATE_CLASS);
      }
    }
  }

  /**
   * Returns SVG path data for progress circle
   * Syntax spec: https://www.w3.org/TR/SVG/paths.html#PathDataEllipticalArcCommands
   *
   * @param {number} diameter Diameter of the container.
   * @param {number} strokeWidth Stroke width to be used when drawing circle
   * @param {boolean} indeterminate Use if progress circle will be used for indeterminate
   *
   * @returns {string} String representation of an SVG arc.
   */
  function getSvgArc(diameter, strokeWidth, indeterminate) {
    var radius = diameter / 2;
    var offset = strokeWidth / 2;
    var start = radius + ',' + offset; // ie: (25, 2.5) or 12 o'clock
    var end = offset + ',' + radius;   // ie: (2.5, 25) or  9 o'clock
    var arcRadius = radius - offset;
    return 'M' + start
         + 'A' + arcRadius + ',' + arcRadius + ' 0 1 1 ' + end // 75% circle
         + (indeterminate ? '' : 'A' + arcRadius + ',' + arcRadius + ' 0 0 1 ' + start); // loop to start
  }

  /**
   * Return stroke length for progress circle
   *
   * @param {number} diameter Diameter of the container.
   * @param {number} strokeWidth Stroke width to be used when drawing circle
   * @param {number} value Percentage of circle (between 0 and 100)
   * @param {number} maxArcLength Maximum length of arc as a percentage of circle (between 0 and 100)
   *
   * @returns {number} Stroke length for progress circle
   */
  function getDashOffset(diameter, strokeWidth, value, maxArcLength) {
    return getSpinnerCircumference(diameter, strokeWidth) * ((maxArcLength - value) / 100);
  }

  /**
   * Limits a value between 0 and 100.
   */
  function clamp(value) {
    return $window.Math.max(0, $window.Math.min(value || 0, 100));
  }

  /**
   * Determines the size of a progress circle, based on the provided
   * value in the following formats: `X`, `Ypx`, `Z%`.
   */
  function getSize(value) {
    var defaultValue = $mdProgressCircular.progressSize;

    if (value) {
      var parsed = parseFloat(value);

      if (value.lastIndexOf('%') === value.length - 1) {
        parsed = (parsed / 100) * defaultValue;
      }

      return parsed;
    }

    return defaultValue;
  }

  /**
   * Determines the circle's stroke width, based on
   * the provided diameter.
   */
  function getStroke(diameter) {
    return $mdProgressCircular.strokeWidth / 100 * diameter;
  }

  /**
   * Return length of the dash
   *
   * @param {number} diameter Diameter of the container.
   * @param {number} strokeWidth Stroke width to be used when drawing circle
   * @param {number} value Percentage of circle (between 0 and 100)
   *
   * @returns {number} Length of the dash
   */
  function getDashLength(diameter, strokeWidth, value) {
    return getSpinnerCircumference(diameter, strokeWidth) * (value / 100);
  }

  /**
   * Return circumference of the spinner
   *
   * @param {number} diameter Diameter of the container.
   * @param {number} strokeWidth Stroke width to be used when drawing circle
   *
   * @returns {number} Circumference of the spinner
   */
  function getSpinnerCircumference(diameter, strokeWidth) {
    return ((diameter - strokeWidth) * $window.Math.PI);
  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc service
 * @name $mdProgressCircular
 * @module material.components.progressCircular
 *
 * @description
 * Allows the user to specify the default options for the `progressCircular` directive.
 *
 * @property {number} progressSize Diameter of the progress circle in pixels.
 * @property {number} strokeWidth Width of the circle's stroke as a percentage of the circle's size.
 * @property {number} duration Length of the circle animation in milliseconds.
 * @property {function} easeFn Default easing animation function.
 * @property {object} easingPresets Collection of pre-defined easing functions.
 *
 * @property {number} durationIndeterminate Duration of the indeterminate animation.
 * @property {number} startIndeterminate Indeterminate animation start point.
 * @property {number} endIndeterminate Indeterminate animation end point.
 * @property {function} easeFnIndeterminate Easing function to be used when animating
 * between the indeterminate values.
 *
 * @property {(function(object): object)} configure Used to modify the default options.
 *
 * @usage
 * <hljs lang="js">
 *   myAppModule.config(function($mdProgressCircularProvider) {
 *
 *     // Example of changing the default progress options.
 *     $mdProgressCircularProvider.configure({
 *       progressSize: 100,
 *       strokeWidth: 20,
 *       duration: 800
 *     });
 * });
 * </hljs>
 *
 */

angular
  .module('material.components.progressCircular')
  .provider("$mdProgressCircular", MdProgressCircularProvider);

function MdProgressCircularProvider() {
  var progressConfig = {
    progressSize: 50,
    strokeWidth: 10,
    duration: 100,
    easeFn: linearEase,

    durationIndeterminate: 1333,
    startIndeterminate: 1,
    endIndeterminate: 149,
    easeFnIndeterminate: materialEase,

    easingPresets: {
      linearEase: linearEase,
      materialEase: materialEase
    }
  };

  return {
    configure: function(options) {
      progressConfig = angular.extend(progressConfig, options || {});
      return progressConfig;
    },
    $get: function() { return progressConfig; }
  };

  function linearEase(t, b, c, d) {
    return c * t / d + b;
  }

  function materialEase(t, b, c, d) {
    // via http://www.timotheegroleau.com/Flash/experiments/easing_function_generator.htm
    // with settings of [0, 0, 1, 1]
    var ts = (t /= d) * t;
    var tc = ts * t;
    return b + c * (6 * tc * ts + -15 * ts * ts + 10 * tc);
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.progressLinear
 * @description Linear Progress module!
 */
MdProgressLinearDirective.$inject = ["$mdTheming", "$mdUtil", "$log"];
angular.module('material.components.progressLinear', [
  'material.core'
])
  .directive('mdProgressLinear', MdProgressLinearDirective);

/**
 * @ngdoc directive
 * @name mdProgressLinear
 * @module material.components.progressLinear
 * @restrict E
 *
 * @description
 * The linear progress directive is used to make loading content
 * in your app as delightful and painless as possible by minimizing
 * the amount of visual change a user sees before they can view
 * and interact with content.
 *
 * Each operation should only be represented by one activity indicator
 * For example: one refresh operation should not display both a
 * refresh bar and an activity circle.
 *
 * For operations where the percentage of the operation completed
 * can be determined, use a determinate indicator. They give users
 * a quick sense of how long an operation will take.
 *
 * For operations where the user is asked to wait a moment while
 * something finishes up, and it’s not necessary to expose what's
 * happening behind the scenes and how long it will take, use an
 * indeterminate indicator.
 *
 * @param {string} md-mode Select from one of four modes: determinate, indeterminate, buffer or query.
 *
 * Note: if the `md-mode` value is set as undefined or specified as 1 of the four (4) valid modes, then `indeterminate`
 * will be auto-applied as the mode.
 *
 * Note: if not configured, the `md-mode="indeterminate"` will be auto injected as an attribute. If `value=""` is also specified, however,
 * then `md-mode="determinate"` would be auto-injected instead.
 * @param {number=} value In determinate and buffer modes, this number represents the percentage of the primary progress bar. Default: 0
 * @param {number=} md-buffer-value In the buffer mode, this number represents the percentage of the secondary progress bar. Default: 0
 * @param {boolean=} ng-disabled Determines whether to disable the progress element.
 *
 * @usage
 * <hljs lang="html">
 * <md-progress-linear md-mode="determinate" value="..."></md-progress-linear>
 *
 * <md-progress-linear md-mode="determinate" ng-value="..."></md-progress-linear>
 *
 * <md-progress-linear md-mode="indeterminate"></md-progress-linear>
 *
 * <md-progress-linear md-mode="buffer" value="..." md-buffer-value="..."></md-progress-linear>
 *
 * <md-progress-linear md-mode="query"></md-progress-linear>
 * </hljs>
 */
function MdProgressLinearDirective($mdTheming, $mdUtil, $log) {
  var MODE_DETERMINATE = "determinate";
  var MODE_INDETERMINATE = "indeterminate";
  var MODE_BUFFER = "buffer";
  var MODE_QUERY = "query";
  var DISABLED_CLASS = "_md-progress-linear-disabled";

  return {
    restrict: 'E',
    template: '<div class="md-container">' +
      '<div class="md-dashed"></div>' +
      '<div class="md-bar md-bar1"></div>' +
      '<div class="md-bar md-bar2"></div>' +
      '</div>',
    compile: compile
  };

  function compile(tElement, tAttrs, transclude) {
    tElement.attr('aria-valuemin', 0);
    tElement.attr('aria-valuemax', 100);
    tElement.attr('role', 'progressbar');

    return postLink;
  }
  function postLink(scope, element, attr) {
    $mdTheming(element);

    var lastMode;
    var isDisabled = attr.hasOwnProperty('disabled');
    var toVendorCSS = $mdUtil.dom.animator.toCss;
    var bar1 = angular.element(element[0].querySelector('.md-bar1'));
    var bar2 = angular.element(element[0].querySelector('.md-bar2'));
    var container = angular.element(element[0].querySelector('.md-container'));

    element
      .attr('md-mode', mode())
      .toggleClass(DISABLED_CLASS, isDisabled);

    validateMode();
    watchAttributes();

    /**
     * Watch the value, md-buffer-value, and md-mode attributes
     */
    function watchAttributes() {
      attr.$observe('value', function(value) {
        var percentValue = clamp(value);
        element.attr('aria-valuenow', percentValue);

        if (mode() != MODE_QUERY) animateIndicator(bar2, percentValue);
      });

      attr.$observe('mdBufferValue', function(value) {
        animateIndicator(bar1, clamp(value));
      });

      attr.$observe('disabled', function(value) {
        if (value === true || value === false) {
          isDisabled = !!value;
        } else {
          isDisabled = angular.isDefined(value);
        }

        element.toggleClass(DISABLED_CLASS, isDisabled);
        container.toggleClass(lastMode, !isDisabled);
      });

      attr.$observe('mdMode', function(mode) {
        if (lastMode) container.removeClass(lastMode);

        switch (mode) {
          case MODE_QUERY:
          case MODE_BUFFER:
          case MODE_DETERMINATE:
          case MODE_INDETERMINATE:
            container.addClass(lastMode = "md-mode-" + mode);
            break;
          default:
            container.addClass(lastMode = "md-mode-" + MODE_INDETERMINATE);
            break;
        }
      });
    }

    /**
     * Auto-defaults the mode to either `determinate` or `indeterminate` mode; if not specified
     */
    function validateMode() {
      if (angular.isUndefined(attr.mdMode)) {
        var hasValue = angular.isDefined(attr.value);
        var mode = hasValue ? MODE_DETERMINATE : MODE_INDETERMINATE;
        var info = "Auto-adding the missing md-mode='{0}' to the ProgressLinear element";
        element.attr("md-mode", mode);
        attr.mdMode = mode;
      }
    }

    /**
     * Is the md-mode a valid option?
     */
    function mode() {
      var value = (attr.mdMode || "").trim();
      if (value) {
        switch (value) {
          case MODE_DETERMINATE:
          case MODE_INDETERMINATE:
          case MODE_BUFFER:
          case MODE_QUERY:
            break;
          default:
            value = MODE_INDETERMINATE;
            break;
        }
      }
      return value;
    }

    /**
     * Manually set CSS to animate the Determinate indicator based on the specified
     * percentage value (0-100).
     */
    function animateIndicator(target, value) {
      if (isDisabled || !mode()) return;

      var to = $mdUtil.supplant("translateX({0}%) scale({1},1)", [(value-100)/2, value/100]);
      var styles = toVendorCSS({ transform : to });
      angular.element(target).css(styles);
    }
  }

  /**
   * Clamps the value to be between 0 and 100.
   * @param {number} value The value to clamp.
   * @returns {number}
   */
  function clamp(value) {
    return Math.max(0, Math.min(value || 0, 100));
  }
}


})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.radioButton
 * @description radioButton module!
 */
mdRadioGroupDirective.$inject = ["$mdUtil", "$mdConstant", "$mdTheming", "$timeout"];
mdRadioButtonDirective.$inject = ["$mdAria", "$mdUtil", "$mdTheming"];
angular.module('material.components.radioButton', [
  'material.core'
])
  .directive('mdRadioGroup', mdRadioGroupDirective)
  .directive('mdRadioButton', mdRadioButtonDirective);

/**
 * @ngdoc directive
 * @module material.components.radioButton
 * @name mdRadioGroup
 *
 * @restrict E
 *
 * @description
 * The `<md-radio-group>` directive identifies a grouping
 * container for the 1..n grouped radio buttons; specified using nested
 * `<md-radio-button>` elements.
 *
 * The radio button uses the accent color by default. The primary color palette may be used with
 * the `md-primary` class.
 *
 * Note: `<md-radio-group>` and `<md-radio-button>` handle `tabindex` differently
 * than the native `<input type="radio">` controls. Whereas the native controls
 * force the user to tab through all the radio buttons, `<md-radio-group>`
 * is focusable and by default the `<md-radio-button>`s are not.
 *
 * @param {string} ng-model Assignable angular expression to data-bind to.
 * @param {string=} ng-change AngularJS expression to be executed when input changes due to user
 *    interaction.
 * @param {boolean=} md-no-ink If present, disables ink ripple effects.
 *
 * @usage
 * <hljs lang="html">
 * <md-radio-group ng-model="selected">
 *   <md-radio-button ng-repeat="item in items"
 *                    ng-value="item.value" aria-label="{{item.label}}">
 *     {{ item.label }}
 *   </md-radio-button>
 * </md-radio-group>
 * </hljs>
 */
function mdRadioGroupDirective($mdUtil, $mdConstant, $mdTheming, $timeout) {
  RadioGroupController.prototype = createRadioGroupControllerProto();

  return {
    restrict: 'E',
    controller: ['$element', RadioGroupController],
    require: ['mdRadioGroup', '?ngModel'],
    link: { pre: linkRadioGroup }
  };

  function linkRadioGroup(scope, element, attr, ctrls) {
    // private md component indicator for styling
    element.addClass('_md');
    $mdTheming(element);

    var rgCtrl = ctrls[0];
    var ngModelCtrl = ctrls[1] || $mdUtil.fakeNgModel();

    rgCtrl.init(ngModelCtrl);

    scope.mouseActive = false;

    element
      .attr({
        'role': 'radiogroup',
        'tabIndex': element.attr('tabindex') || '0'
      })
      .on('keydown', keydownListener)
      .on('mousedown', function(event) {
        scope.mouseActive = true;
        $timeout(function() {
          scope.mouseActive = false;
        }, 100);
      })
      .on('focus', function() {
        if (scope.mouseActive === false) {
          rgCtrl.$element.addClass('md-focused');
        }
      })
      .on('blur', function() {
        rgCtrl.$element.removeClass('md-focused');
      });

    /**
     * Apply the md-focused class if it isn't already applied.
     */
    function setFocus() {
      if (!element.hasClass('md-focused')) { element.addClass('md-focused'); }
    }

    /**
     * @param {KeyboardEvent} ev
     */
    function keydownListener(ev) {
      var keyCode = ev.which || ev.keyCode;

      // Only listen to events that we originated ourselves
      // so that we don't trigger on things like arrow keys in inputs.
      if (keyCode !== $mdConstant.KEY_CODE.ENTER &&
          ev.currentTarget !== ev.target) {
        return;
      }

      switch (keyCode) {
        case $mdConstant.KEY_CODE.LEFT_ARROW:
        case $mdConstant.KEY_CODE.UP_ARROW:
          ev.preventDefault();
          rgCtrl.selectPrevious();
          setFocus();
          break;

        case $mdConstant.KEY_CODE.RIGHT_ARROW:
        case $mdConstant.KEY_CODE.DOWN_ARROW:
          ev.preventDefault();
          rgCtrl.selectNext();
          setFocus();
          break;

        case $mdConstant.KEY_CODE.ENTER:
          var form = angular.element($mdUtil.getClosest(element[0], 'form'));
          if (form.length > 0) {
            form.triggerHandler('submit');
          }
          break;
      }
    }
  }

  function RadioGroupController($element) {
    this._radioButtonRenderFns = [];
    this.$element = $element;
  }

  function createRadioGroupControllerProto() {
    return {
      init: function(ngModelCtrl) {
        this._ngModelCtrl = ngModelCtrl;
        this._ngModelCtrl.$render = angular.bind(this, this.render);
      },
      add: function(rbRender) {
        this._radioButtonRenderFns.push(rbRender);
      },
      remove: function(rbRender) {
        var index = this._radioButtonRenderFns.indexOf(rbRender);
        if (index !== -1) {
          this._radioButtonRenderFns.splice(index, 1);
        }
      },
      render: function() {
        this._radioButtonRenderFns.forEach(function(rbRender) {
          rbRender();
        });
      },
      setViewValue: function(value, eventType) {
        this._ngModelCtrl.$setViewValue(value, eventType);
        // update the other radio buttons as well
        this.render();
      },
      getViewValue: function() {
        return this._ngModelCtrl.$viewValue;
      },
      selectNext: function() {
        return changeSelectedButton(this.$element, 1);
      },
      selectPrevious: function() {
        return changeSelectedButton(this.$element, -1);
      },
      setActiveDescendant: function (radioId) {
        this.$element.attr('aria-activedescendant', radioId);
      },
      isDisabled: function() {
        return this.$element[0].hasAttribute('disabled');
      }
    };
  }
  /**
   * Change the radio group's selected button by a given increment.
   * If no button is selected, select the first button.
   */
  function changeSelectedButton(parent, increment) {
    // Coerce all child radio buttons into an array, then wrap then in an iterator
    var buttons = $mdUtil.iterator(parent[0].querySelectorAll('md-radio-button'), true);

    if (buttons.count()) {
      var validate = function (button) {
        // If disabled, then NOT valid
        return !angular.element(button).attr("disabled");
      };

      var selected = parent[0].querySelector('md-radio-button.md-checked');
      var target = buttons[increment < 0 ? 'previous' : 'next'](selected, validate) || buttons.first();

      // Activate radioButton's click listener (triggerHandler won't create a real click event)
      angular.element(target).triggerHandler('click');
    }
  }

}

/**
 * @ngdoc directive
 * @module material.components.radioButton
 * @name mdRadioButton
 *
 * @restrict E
 *
 * @description
 * The `<md-radio-button>`directive is the child directive required to be used within `<md-radio-group>` elements.
 *
 * While similar to the `<input type="radio" ng-model="" value="">` directive,
 * the `<md-radio-button>` directive provides ink effects, ARIA support, and
 * supports use within named radio groups.
 *
 * One of `value` or `ng-value` must be set so that the `md-radio-group`'s model is set properly when the
 * `md-radio-button` is selected.
 *
 * @param {string} value The value to which the model should be set when selected.
 * @param {string} ng-value AngularJS expression which sets the value to which the model should
 *    be set when selected.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} aria-label Adds label to radio button for accessibility.
 *    Defaults to radio button's text. If no text content is available, a warning will be logged.
 *
 * @usage
 * <hljs lang="html">
 *
 * <md-radio-button value="1" aria-label="Label 1">
 *   Label 1
 * </md-radio-button>
 *
 * <md-radio-button ng-value="specialValue" aria-label="Green">
 *   Green
 * </md-radio-button>
 *
 * </hljs>
 *
 */
function mdRadioButtonDirective($mdAria, $mdUtil, $mdTheming) {

  var CHECKED_CSS = 'md-checked';

  return {
    restrict: 'E',
    require: '^mdRadioGroup',
    transclude: true,
    template: '<div class="md-container" md-ink-ripple md-ink-ripple-checkbox>' +
                '<div class="md-off"></div>' +
                '<div class="md-on"></div>' +
              '</div>' +
              '<div ng-transclude class="md-label"></div>',
    link: link
  };

  function link(scope, element, attr, rgCtrl) {
    var lastChecked;

    $mdTheming(element);
    configureAria(element, scope);

    // ngAria overwrites the aria-checked inside a $watch for ngValue.
    // We should defer the initialization until all the watches have fired.
    // This can also be fixed by removing the `lastChecked` check, but that'll
    // cause more DOM manipulation on each digest.
    if (attr.ngValue) {
      $mdUtil.nextTick(initialize, false);
    } else {
      initialize();
    }

    /**
     * Initializes the component.
     */
    function initialize() {
      if (!rgCtrl) {
        throw 'RadioButton: No RadioGroupController could be found.';
      }

      rgCtrl.add(render);
      attr.$observe('value', render);

      element
        .on('click', listener)
        .on('$destroy', function() {
          rgCtrl.remove(render);
        });
    }

    /**
     * On click functionality.
     */
    function listener(ev) {
      if (element[0].hasAttribute('disabled') || rgCtrl.isDisabled()) return;

      scope.$apply(function() {
        rgCtrl.setViewValue(attr.value, ev && ev.type);
      });
    }

    /**
     *  Add or remove the `.md-checked` class from the RadioButton (and conditionally its parent).
     *  Update the `aria-activedescendant` attribute.
     */
    function render() {
      var checked = rgCtrl.getViewValue() == attr.value;

      if (checked === lastChecked) return;

      if (element[0].parentNode.nodeName.toLowerCase() !== 'md-radio-group') {
        // If the radioButton is inside a div, then add class so highlighting will work
        element.parent().toggleClass(CHECKED_CSS, checked);
      }

      if (checked) {
        rgCtrl.setActiveDescendant(element.attr('id'));
      }

      lastChecked = checked;

      element
        .attr('aria-checked', checked)
        .toggleClass(CHECKED_CSS, checked);
    }

    /**
     * Inject ARIA-specific attributes appropriate for each radio button
     */
    function configureAria(element, scope){
      element.attr({
        id: attr.id || 'radio_' + $mdUtil.nextUid(),
        role: 'radio',
        'aria-checked': 'false'
      });

      $mdAria.expectWithText(element, 'aria-label');
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.select
 */

/***************************************************

 ### TODO ###
 - [ ] Abstract placement logic in $mdSelect service to $mdMenu service

 ***************************************************/

SelectDirective.$inject = ["$mdSelect", "$mdUtil", "$mdConstant", "$mdTheming", "$mdAria", "$parse", "$sce", "$injector"];
SelectMenuDirective.$inject = ["$parse", "$mdUtil", "$mdConstant", "$mdTheming"];
OptionDirective.$inject = ["$mdButtonInkRipple", "$mdUtil", "$mdTheming"];
SelectProvider.$inject = ["$$interimElementProvider"];
OptionController.$inject = ["$element"];
var SELECT_EDGE_MARGIN = 8;
var selectNextId = 0;
var CHECKBOX_SELECTION_INDICATOR =
  angular.element('<div class="md-container"><div class="md-icon"></div></div>');

angular.module('material.components.select', [
    'material.core',
    'material.components.backdrop'
  ])
  .directive('mdSelect', SelectDirective)
  .directive('mdSelectMenu', SelectMenuDirective)
  .directive('mdOption', OptionDirective)
  .directive('mdOptgroup', OptgroupDirective)
  .directive('mdSelectHeader', SelectHeaderDirective)
  .provider('$mdSelect', SelectProvider);

/**
 * @ngdoc directive
 * @name mdSelect
 * @restrict E
 * @module material.components.select
 *
 * @description Displays a select box, bound to an `ng-model`. Selectable options are defined using
 * the <a ng-href="api/directive/mdOption">md-option</a> element directive. Options can be grouped
 * using the <a ng-href="api/directive/mdOptgroup">md-optgroup</a> element directive.
 *
 * When the select is required and uses a floating label, then the label will automatically contain
 * an asterisk (`*`). This behavior can be disabled by using the `md-no-asterisk` attribute.
 *
 * By default, the select will display with an underline to match other form elements. This can be
 * disabled by applying the `md-no-underline` CSS class.
 *
 * @param {expression} ng-model Assignable angular expression to data-bind to.
 * @param {expression=} ng-change Expression to be executed when the model value changes.
 * @param {boolean=} multiple When present, allows for more than one option to be selected.
 *  The model is an array with the selected choices. **Note:** This attribute is only evaluated
 *  once; it is not watched.
 * @param {expression=} md-on-close Expression to be evaluated when the select is closed.
 * @param {expression=} md-on-open Expression to be evaluated when opening the select.
 *  Will hide the select options and show a spinner until the evaluated promise resolves.
 * @param {expression=} md-selected-text Expression to be evaluated that will return a string
 *  to be displayed as a placeholder in the select input box when it is closed. The value
 *  will be treated as *text* (not html).
 * @param {expression=} md-selected-html Expression to be evaluated that will return a string
 *  to be displayed as a placeholder in the select input box when it is closed. The value
 *  will be treated as *html*. The value must either be explicitly marked as trustedHtml or
 *  the ngSanitize module must be loaded.
 * @param {string=} placeholder Placeholder hint text.
 * @param {boolean=} md-no-asterisk When set to true, an asterisk will not be appended to the
 *  floating label. **Note:** This attribute is only evaluated once; it is not watched.
 * @param {string=} aria-label Optional label for accessibility. Only necessary if no explicit label
 *  is present.
 * @param {string=} md-container-class Class list to get applied to the `.md-select-menu-container`
 *  element (for custom styling).
 * @param {string=} md-select-only-option If specified, a `<md-select>` will automatically select
 * it's first option, if it only has one.
 *
 * @usage
 * With a placeholder (label and aria-label are added dynamically)
 * <hljs lang="html">
 *   <md-input-container>
 *     <md-select
 *       ng-model="someModel"
 *       placeholder="Select a state">
 *       <md-option ng-value="opt" ng-repeat="opt in neighborhoods2">{{ opt }}</md-option>
 *     </md-select>
 *   </md-input-container>
 * </hljs>
 *
 * With an explicit label
 * <hljs lang="html">
 *   <md-input-container>
 *     <label>State</label>
 *     <md-select
 *       ng-model="someModel">
 *       <md-option ng-value="opt" ng-repeat="opt in neighborhoods2">{{ opt }}</md-option>
 *     </md-select>
 *   </md-input-container>
 * </hljs>
 *
 * Using the `md-select-header` element directive
 *
 * When a developer needs to put more than just a text label in the `md-select-menu`, they should
 * use one or more `md-select-header`s. These elements can contain custom HTML which can be styled
 * as desired. Use cases for this element include a sticky search bar and custom option group
 * labels.
 *
 * <hljs lang="html">
 *   <md-input-container>
 *     <md-select ng-model="someModel">
 *       <md-select-header>
 *         <span> Neighborhoods - </span>
 *       </md-select-header>
 *       <md-option ng-value="opt" ng-repeat="opt in neighborhoods2">{{ opt }}</md-option>
 *     </md-select>
 *   </md-input-container>
 * </hljs>
 *
 * ## Selects and object equality
 * When using a `md-select` to pick from a list of objects, it is important to realize how javascript handles
 * equality. Consider the following example:
 * <hljs lang="js">
 * angular.controller('MyCtrl', function($scope) {
 *   $scope.users = [
 *     { id: 1, name: 'Bob' },
 *     { id: 2, name: 'Alice' },
 *     { id: 3, name: 'Steve' }
 *   ];
 *   $scope.selectedUser = { id: 1, name: 'Bob' };
 * });
 * </hljs>
 * <hljs lang="html">
 * <div ng-controller="MyCtrl">
 *   <md-select ng-model="selectedUser">
 *     <md-option ng-value="user" ng-repeat="user in users">{{ user.name }}</md-option>
 *   </md-select>
 * </div>
 * </hljs>
 *
 * At first one might expect that the select should be populated with "Bob" as the selected user.
 * However, this is not true. To determine whether something is selected,
 * `ngModelController` is looking at whether `$scope.selectedUser == (any user in $scope.users);`;
 *
 * Javascript's `==` operator does not check for deep equality (ie. that all properties
 * on the object are the same), but instead whether the objects are *the same object in memory*.
 * In this case, we have two instances of identical objects, but they exist in memory as unique
 * entities. Because of this, the select will have no value populated for a selected user.
 *
 * To get around this, `ngModelController` provides a `track by` option that allows us to specify a
 * different expression which will be used for the equality operator. As such, we can update our
 * `html` to make use of this by specifying the `ng-model-options="{trackBy: '$value.id'}"` on the
 * `md-select` element. This converts our equality expression to be
 * `$scope.selectedUser.id == (any id in $scope.users.map(function(u) { return u.id; }));`
 * which results in Bob being selected as desired.
 *
 * **Note:** We do not support AngularJS's `track by` syntax. For instance
 *  `ng-options="user in users track by user.id"` will not work with `md-select`.
 *
 * Working HTML:
 * <hljs lang="html">
 * <div ng-controller="MyCtrl">
 *   <md-select ng-model="selectedUser" ng-model-options="{trackBy: '$value.id'}">
 *     <md-option ng-value="user" ng-repeat="user in users">{{ user.name }}</md-option>
 *   </md-select>
 * </div>
 * </hljs>
 */
function SelectDirective($mdSelect, $mdUtil, $mdConstant, $mdTheming, $mdAria, $parse, $sce,
    $injector) {
  return {
    restrict: 'E',
    require: ['^?mdInputContainer', 'mdSelect', 'ngModel', '?^form'],
    compile: compile,
    controller: function() {
    } // empty placeholder controller to be initialized in link
  };

  function compile(tElement, tAttrs) {
    var isMultiple = $mdUtil.parseAttributeBoolean(tAttrs.multiple);
    // add the select value that will hold our placeholder or selected option value
    var valueEl = angular.element('<md-select-value><span></span></md-select-value>');
    valueEl.append('<span class="md-select-icon" aria-hidden="true"></span>');
    valueEl.addClass('md-select-value');
    if (!valueEl[0].hasAttribute('id')) {
      valueEl.attr('id', 'select_value_label_' + $mdUtil.nextUid());
    }

    // There's got to be an md-content inside. If there's not one, let's add it.
    var mdContentEl = tElement.find('md-content');
    if (!mdContentEl.length) {
      tElement.append(angular.element('<md-content>').append(tElement.contents()));
      mdContentEl = tElement.find('md-content');
    }
    mdContentEl.attr('role', 'listbox');
    mdContentEl.attr('tabindex', '-1');

    if (isMultiple) {
      mdContentEl.attr('aria-multiselectable', 'true');
    } else {
      mdContentEl.attr('aria-multiselectable', 'false');
    }

    // Add progress spinner for md-options-loading
    if (tAttrs.mdOnOpen) {

      // Show progress indicator while loading async
      // Use ng-hide for `display:none` so the indicator does not interfere with the options list
      tElement
        .find('md-content')
        .prepend(angular.element(
          '<div>' +
          ' <md-progress-circular md-mode="indeterminate" ng-if="$$loadingAsyncDone === false"' +
          ' md-diameter="25px"></md-progress-circular>' +
          '</div>'
        ));

      // Hide list [of item options] while loading async
      tElement
        .find('md-option')
        .attr('ng-show', '$$loadingAsyncDone');
    }

    if (tAttrs.name) {
      var autofillClone = angular.element('<select class="md-visually-hidden"></select>');
      autofillClone.attr({
        'name': tAttrs.name,
        'aria-hidden': 'true',
        'tabindex': '-1'
      });
      var opts = tElement.find('md-option');
      angular.forEach(opts, function(el) {
        var newEl = angular.element('<option>' + el.innerHTML + '</option>');
        if (el.hasAttribute('ng-value')) {
          newEl.attr('ng-value', el.getAttribute('ng-value'));
        }
        else if (el.hasAttribute('value')) {
          newEl.attr('value', el.getAttribute('value'));
        }
        autofillClone.append(newEl);
      });

      // Adds an extra option that will hold the selected value for the
      // cases where the select is a part of a non-AngularJS form. This can be done with a ng-model,
      // however if the `md-option` is being `ng-repeat`-ed, AngularJS seems to insert a similar
      // `option` node, but with a value of `? string: <value> ?` which would then get submitted.
      // This also goes around having to prepend a dot to the name attribute.
      autofillClone.append(
        '<option ng-value="' + tAttrs.ngModel + '" selected></option>'
      );

      tElement.parent().append(autofillClone);
    }

    // Use everything that's left inside element.contents() as the contents of the menu
    var multipleContent = isMultiple ? 'multiple' : '';
    var ngModelOptions = tAttrs.ngModelOptions ? $mdUtil.supplant('ng-model-options="{0}"', [tAttrs.ngModelOptions]) : '';
    var selectTemplate = '' +
      '<div class="md-select-menu-container" aria-hidden="true" role="presentation">' +
      '  <md-select-menu role="presentation" {0} {1}>{2}</md-select-menu>' +
      '</div>';

    selectTemplate = $mdUtil.supplant(selectTemplate, [multipleContent, ngModelOptions,  tElement.html()]);
    tElement.empty().append(valueEl);
    tElement.append(selectTemplate);

    if (!tAttrs.tabindex) {
      tAttrs.$set('tabindex', 0);
    }

    return function postLink(scope, element, attrs, ctrls) {
      var untouched = true;
      var isDisabled;

      var containerCtrl = ctrls[0];
      var mdSelectCtrl = ctrls[1];
      var ngModelCtrl = ctrls[2];
      var formCtrl = ctrls[3];
      // grab a reference to the select menu value label
      var selectValueElement = element.find('md-select-value');
      var isReadonly = angular.isDefined(attrs.readonly);
      var disableAsterisk = $mdUtil.parseAttributeBoolean(attrs.mdNoAsterisk);
      var stopNgMultipleWatch;
      var userDefinedLabelledby = angular.isDefined(attrs.ariaLabelledby);
      var listboxContentElement = element.find('md-content');

      if (disableAsterisk) {
        element.addClass('md-no-asterisk');
      }

      if (containerCtrl) {
        var isErrorGetter = containerCtrl.isErrorGetter || function() {
          return ngModelCtrl.$invalid && (ngModelCtrl.$touched || (formCtrl && formCtrl.$submitted));
        };

        if (containerCtrl.input) {
          // We ignore inputs that are in the md-select-header (one
          // case where this might be useful would be adding as searchbox)
          if (element.find('md-select-header').find('input')[0] !== containerCtrl.input[0]) {
            throw new Error("<md-input-container> can only have *one* child <input>, <textarea> or <select> element!");
          }
        }

        containerCtrl.input = element;
        if (!containerCtrl.label) {
          $mdAria.expect(element, 'aria-label', element.attr('placeholder'));
          var selectLabel = element.attr('aria-label');
          if (!selectLabel) {
            selectLabel = element.attr('placeholder');
          }
          listboxContentElement.attr('aria-label', selectLabel);
        } else {
          containerCtrl.label.attr('aria-hidden', 'true');
          listboxContentElement.attr('aria-label', containerCtrl.label.text());
        }

        var stopInvalidWatch = scope.$watch(isErrorGetter, containerCtrl.setInvalid);
      }

      var selectContainer, selectScope, selectMenuCtrl;

      selectContainer = findSelectContainer();
      $mdTheming(element);

      var originalRender = ngModelCtrl.$render;
      ngModelCtrl.$render = function() {
        originalRender();
        syncSelectValueText();
        inputCheckValue();
      };

      var stopPlaceholderObserver = attrs.$observe('placeholder', ngModelCtrl.$render);

      var stopRequiredObserver = attrs.$observe('required', function (value) {
        if (containerCtrl && containerCtrl.label) {
          // Toggle the md-required class on the input containers label, because the input container
          // is automatically applying the asterisk indicator on the label.
          containerCtrl.label.toggleClass('md-required', value && !disableAsterisk);
        }
        element.removeAttr('aria-required');
        if (value) {
          listboxContentElement.attr('aria-required', 'true');
        } else {
          listboxContentElement.removeAttr('aria-required');
        }
      });

      /**
       * Set the contents of the md-select-value element. This element's contents are announced by
       * screen readers and used for displaying the value of the select in both single and multiple
       * selection modes.
       * @param {string=} text A sanitized and trusted HTML string or a pure text string from user
       *  input.
       */
      mdSelectCtrl.setSelectValueText = function(text) {
        var useDefaultText = text === undefined || text === '';
        // Whether the select label has been given via user content rather than the internal
        // template of <md-option>
        var isSelectLabelFromUser = false;

        mdSelectCtrl.setIsPlaceholder(!text);

        if (attrs.mdSelectedText && attrs.mdSelectedHtml) {
          throw Error('md-select cannot have both `md-selected-text` and `md-selected-html`');
        }

        if (attrs.mdSelectedText || attrs.mdSelectedHtml) {
          text = $parse(attrs.mdSelectedText || attrs.mdSelectedHtml)(scope);
          isSelectLabelFromUser = true;
        } else if (useDefaultText) {
          // Use placeholder attribute, otherwise fallback to the md-input-container label
          var tmpPlaceholder = attrs.placeholder ||
              (containerCtrl && containerCtrl.label ? containerCtrl.label.text() : '');

          text = tmpPlaceholder || '';
          isSelectLabelFromUser = true;
        }

        var target = selectValueElement.children().eq(0);

        if (attrs.mdSelectedHtml) {
          // Using getTrustedHtml will run the content through $sanitize if it is not already
          // explicitly trusted. If the ngSanitize module is not loaded, this will
          // *correctly* throw an sce error.
          target.html($sce.getTrustedHtml(text));
        } else if (isSelectLabelFromUser) {
          target.text(text);
        } else {
          // If we've reached this point, the text is not user-provided.
          target.html(text);
        }

        if (useDefaultText) {
          // Avoid screen readers double announcing the label name when no value has been selected
          selectValueElement.attr('aria-hidden', 'true');
          if (!userDefinedLabelledby) {
            element.removeAttr('aria-labelledby');
          }
        } else {
          selectValueElement.removeAttr('aria-hidden');
          if (!userDefinedLabelledby) {
            element.attr('aria-labelledby', element[0].id + ' ' + selectValueElement[0].id)
          }
        }
      };

      /**
       * @param {boolean} isPlaceholder true to mark the md-select-value element and
       *  input container, if one exists, with classes for styling when a placeholder is present.
       *  false to remove those classes.
       */
      mdSelectCtrl.setIsPlaceholder = function(isPlaceholder) {
        if (isPlaceholder) {
          selectValueElement.addClass('md-select-placeholder');
          if (containerCtrl && containerCtrl.label) {
            containerCtrl.label.addClass('md-placeholder');
          }
        } else {
          selectValueElement.removeClass('md-select-placeholder');
          if (containerCtrl && containerCtrl.label) {
            containerCtrl.label.removeClass('md-placeholder');
          }
        }
      };

      if (!isReadonly) {
        var handleBlur = function(event) {
          // Attach before ngModel's blur listener to stop propagation of blur event
          // and prevent setting $touched.
          if (untouched) {
            untouched = false;
            if (selectScope._mdSelectIsOpen) {
              event.stopImmediatePropagation();
            }
          }

          containerCtrl && containerCtrl.setFocused(false);
          inputCheckValue();
        };
        var handleFocus = function(ev) {
          // Always focus the container (if we have one) so floating labels and other styles are
          // applied properly
          containerCtrl && containerCtrl.setFocused(true);
        };

        element.on('focus', handleFocus);
        element.on('blur', handleBlur);
      }

      mdSelectCtrl.triggerClose = function() {
        $parse(attrs.mdOnClose)(scope);
      };

      scope.$$postDigest(function() {
        initAriaLabel();
        syncSelectValueText();
      });

      function initAriaLabel() {
        var labelText = element.attr('aria-label') || element.attr('placeholder');
        if (!labelText && containerCtrl && containerCtrl.label) {
          labelText = containerCtrl.label.text();
        }
        $mdAria.expect(element, 'aria-label', labelText);
      }

      var stopSelectedLabelsWatcher = scope.$watch(function() {
        return selectMenuCtrl.getSelectedLabels();
      }, syncSelectValueText);

      function syncSelectValueText() {
        selectMenuCtrl = selectMenuCtrl ||
          selectContainer.find('md-select-menu').controller('mdSelectMenu');
        mdSelectCtrl.setSelectValueText(selectMenuCtrl.getSelectedLabels());
      }

      // TODO add tests for ngMultiple
      // TODO add docs for ngMultiple
      // TODO in 1.2.0 rename this to mdMultiple
      var stopNgMultipleObserver = attrs.$observe('ngMultiple', function(val) {
        if (stopNgMultipleWatch) {
          stopNgMultipleWatch();
        }
        var parser = $parse(val);
        stopNgMultipleWatch = scope.$watch(function() {
          return parser(scope);
        }, function(multiple, prevVal) {
          var selectMenu = selectContainer.find('md-select-menu');
          // assume compiler did a good job
          if (multiple === undefined && prevVal === undefined) {
            return;
          }
          if (multiple) {
            var setMultipleAttrs = {'multiple': 'multiple'};
            element.attr(setMultipleAttrs);
            selectMenu.attr(setMultipleAttrs);
          } else {
            element.removeAttr('multiple');
            selectMenu.removeAttr('multiple');
          }
          element.find('md-content').attr('aria-multiselectable', multiple ? 'true' : 'false');

          if (selectContainer) {
            selectMenuCtrl.setMultiple(Boolean(multiple));
            originalRender = ngModelCtrl.$render;
            ngModelCtrl.$render = function() {
              originalRender();
              syncSelectValueText();
              inputCheckValue();
            };
            ngModelCtrl.$render();
          }
        });
      });

      var stopDisabledObserver = attrs.$observe('disabled', function(disabled) {
        if (angular.isString(disabled)) {
          disabled = true;
        }
        // Prevent click event being registered twice
        if (isDisabled !== undefined && isDisabled === disabled) {
          return;
        }
        isDisabled = disabled;
        if (disabled) {
          element
            .attr({'aria-disabled': 'true'})
            .removeAttr('tabindex')
            .removeAttr('aria-expanded')
            .removeAttr('aria-haspopup')
            .off('click', openSelect)
            .off('keydown', handleKeypress);
        } else {
          element
            .attr({
              'tabindex': attrs.tabindex,
              'aria-haspopup': 'listbox'
            })
            .removeAttr('aria-disabled')
            .on('click', openSelect)
            .on('keydown', handleKeypress);
        }
      });

      if (!attrs.hasOwnProperty('disabled') && !attrs.hasOwnProperty('ngDisabled')) {
        element.attr({'aria-disabled': 'false'});
        element.on('click', openSelect);
        element.on('keydown', handleKeypress);
      }

      var ariaAttrs = {
        role: 'button',
        'aria-haspopup': 'listbox'
      };

      if (!element[0].hasAttribute('id')) {
        ariaAttrs.id = 'select_' + $mdUtil.nextUid();
      }

      var containerId = 'select_container_' + $mdUtil.nextUid();
      selectContainer.attr('id', containerId);
      var listboxContentId = 'select_listbox_' + $mdUtil.nextUid();
      selectContainer.find('md-content').attr('id', listboxContentId);
      // Only add aria-owns if element ownership is NOT represented in the DOM.
      if (!element.find('md-select-menu').length) {
        ariaAttrs['aria-owns'] = listboxContentId;
      }
      element.attr(ariaAttrs);

      scope.$on('$destroy', function() {
        stopRequiredObserver && stopRequiredObserver();
        stopDisabledObserver && stopDisabledObserver();
        stopNgMultipleWatch && stopNgMultipleWatch();
        stopNgMultipleObserver && stopNgMultipleObserver();
        stopSelectedLabelsWatcher && stopSelectedLabelsWatcher();
        stopPlaceholderObserver && stopPlaceholderObserver();
        stopInvalidWatch && stopInvalidWatch();

        element.off('focus');
        element.off('blur');

        $mdSelect
          .destroy()
          .finally(function() {
            if (containerCtrl) {
              containerCtrl.setFocused(false);
              containerCtrl.setHasValue(false);
              containerCtrl.input = null;
            }
            ngModelCtrl.$setTouched();
          });
      });

      function inputCheckValue() {
        // The select counts as having a value if one or more options are selected,
        // or if the input's validity state says it has bad input (eg string in a number input)
        // we must do this on nextTick as the $render is sometimes invoked on nextTick.
        $mdUtil.nextTick(function () {
          containerCtrl && containerCtrl.setHasValue(
            selectMenuCtrl.getSelectedLabels().length > 0 || (element[0].validity || {}).badInput);
        });
      }

      function findSelectContainer() {
        var selectContainer = angular.element(
          element[0].querySelector('.md-select-menu-container')
        );
        selectScope = scope;
        attrs.mdContainerClass && selectContainer.addClass(attrs.mdContainerClass);
        selectMenuCtrl = selectContainer.find('md-select-menu').controller('mdSelectMenu');
        selectMenuCtrl.init(ngModelCtrl, attrs);
        element.on('$destroy', function() {
          selectContainer.remove();
        });
        return selectContainer;
      }

      /**
       * Determine if the select menu should be opened or an option in the select menu should be
       * selected.
       * @param {KeyboardEvent} e keyboard event to handle
       */
      function handleKeypress(e) {
        if ($mdConstant.isNavigationKey(e)) {
          // prevent page scrolling on interaction
          e.preventDefault();
          openSelect(e);
        } else {
          if (shouldHandleKey(e, $mdConstant)) {
            e.preventDefault();

            var node = selectMenuCtrl.optNodeForKeyboardSearch(e);
            if (!node || node.hasAttribute('disabled')) {
              return;
            }
            var optionCtrl = angular.element(node).controller('mdOption');
            if (!selectMenuCtrl.isMultiple) {
              angular.forEach(Object.keys(selectMenuCtrl.selected), function (key) {
                selectMenuCtrl.deselect(key);
              });
            }
            selectMenuCtrl.select(optionCtrl.hashKey, optionCtrl.value);
            selectMenuCtrl.refreshViewValue();
          }
        }
      }

      function openSelect() {
        selectScope._mdSelectIsOpen = true;
        element.attr('aria-expanded', 'true');

        $mdSelect.show({
          scope: selectScope,
          preserveScope: true,
          skipCompile: true,
          element: selectContainer,
          target: element[0],
          selectCtrl: mdSelectCtrl,
          preserveElement: true,
          hasBackdrop: true,
          loadingAsync: attrs.mdOnOpen ? scope.$eval(attrs.mdOnOpen) || true : false
        }).finally(function() {
          selectScope._mdSelectIsOpen = false;
          element.removeAttr('aria-expanded');
          element.removeAttr('aria-activedescendant');
          ngModelCtrl.$setTouched();
        });
      }

    };
  }
}

function SelectMenuDirective($parse, $mdUtil, $mdConstant, $mdTheming) {
  // We want the scope to be set to 'false' so an isolated scope is not created
  // which would interfere with the md-select-header's access to the
  // parent scope.
  SelectMenuController.$inject = ["$scope", "$attrs", "$element"];
  return {
    restrict: 'E',
    require: ['mdSelectMenu'],
    scope: false,
    controller: SelectMenuController,
    link: {pre: preLink}
  };

  // We use preLink instead of postLink to ensure that the select is initialized before
  // its child options run postLink.
  function preLink(scope, element, attrs, ctrls) {
    var selectMenuCtrl = ctrls[0];

    element.addClass('_md');     // private md component indicator for styling

    $mdTheming(element);
    element.on('click', clickListener);
    element.on('keypress', keyListener);

    function keyListener(e) {
      if (e.keyCode === 13 || e.keyCode === 32) {
        clickListener(e);
      }
    }

    function clickListener(ev) {
      var option = $mdUtil.getClosest(ev.target, 'md-option');
      var optionCtrl = option && angular.element(option).data('$mdOptionController');
      if (!option || !optionCtrl) return;
      if (option.hasAttribute('disabled')) {
        ev.stopImmediatePropagation();
        return false;
      }

      var optionHashKey = selectMenuCtrl.hashGetter(optionCtrl.value);
      var isSelected = angular.isDefined(selectMenuCtrl.selected[optionHashKey]);

      scope.$apply(function() {
        if (selectMenuCtrl.isMultiple) {
          if (isSelected) {
            selectMenuCtrl.deselect(optionHashKey);
          } else {
            selectMenuCtrl.select(optionHashKey, optionCtrl.value);
          }
        } else {
          if (!isSelected) {
            angular.forEach(Object.keys(selectMenuCtrl.selected), function (key) {
              selectMenuCtrl.deselect(key);
            });
            selectMenuCtrl.select(optionHashKey, optionCtrl.value);
          }
        }
        selectMenuCtrl.refreshViewValue();
      });
    }
  }

  function SelectMenuController($scope, $attrs, $element) {
    var self = this;
    var defaultIsEmpty;
    var searchStr = '';
    var clearSearchTimeout, optNodes, optText;
    var CLEAR_SEARCH_AFTER = 300;

    self.isMultiple = angular.isDefined($attrs.multiple);
    // selected is an object with keys matching all of the selected options' hashed values
    self.selected = {};
    // options is an object with keys matching every option's hash value,
    // and values containing an instance of every option's controller.
    self.options = {};

    $scope.$watchCollection(function() {
      return self.options;
    }, function() {
      self.ngModel.$render();
      updateOptionSetSizeAndPosition();
    });

    /**
     * @param {boolean} isMultiple
     */
    self.setMultiple = function(isMultiple) {
      var ngModel = self.ngModel;
      defaultIsEmpty = defaultIsEmpty || ngModel.$isEmpty;
      self.isMultiple = isMultiple;

      if (self.isMultiple) {
        // We want to delay the render method so that the directive has a chance to load before
        // rendering, this prevents the control being marked as dirty onload.
        var loaded = false;
        var delayedRender = function(val) {
          if (!loaded) {
            $mdUtil.nextTick(function () {
              renderMultiple(val);
              loaded = true;
            });
          } else {
            renderMultiple(val);
          }
        };
        ngModel.$validators['md-multiple'] = validateArray;
        ngModel.$render = delayedRender;

        // watchCollection on the model because by default ngModel only watches the model's
        // reference. This allows the developer to also push and pop from their array.
        $scope.$watchCollection(self.modelBinding, function(value) {
          if (validateArray(value)) {
            delayedRender(value);
          }
        });

        ngModel.$isEmpty = function(value) {
          return !value || value.length === 0;
        };
      } else {
        delete ngModel.$validators['md-multiple'];
        ngModel.$render = renderSingular;
      }

      function validateArray(modelValue, viewValue) {
        // If a value is truthy but not an array, reject it.
        // If value is undefined/falsy, accept that it's an empty array.
        return angular.isArray(modelValue || viewValue || []);
      }
    };

    /**
     * @param {KeyboardEvent} keyboardEvent keyboard event to handle
     * @return {Element|HTMLElement|undefined}
     */
    self.optNodeForKeyboardSearch = function(keyboardEvent) {
      var search, i;
      clearSearchTimeout && clearTimeout(clearSearchTimeout);
      clearSearchTimeout = setTimeout(function() {
        clearSearchTimeout = undefined;
        searchStr = '';
        optText = undefined;
        optNodes = undefined;
      }, CLEAR_SEARCH_AFTER);

      searchStr += keyboardEvent.key;
      search = new RegExp('^' + $mdUtil.sanitize(searchStr), 'i');
      if (!optNodes) {
        optNodes = $element.find('md-option');
        optText = new Array(optNodes.length);
        angular.forEach(optNodes, function(el, i) {
          optText[i] = el.textContent.trim();
        });
      }

      for (i = 0; i < optText.length; ++i) {
        if (search.test(optText[i])) {
          return optNodes[i];
        }
      }
    };

    self.init = function(ngModel, parentAttrs) {
      self.ngModel = ngModel;
      self.modelBinding = parentAttrs.ngModel;

      // Setup a more robust version of isEmpty to ensure value is a valid option
      self.ngModel.$isEmpty = function($viewValue) {
        // We have to transform the viewValue into the hashKey, because otherwise the
        // OptionCtrl may not exist. Developers may have specified a trackBy function.
        return !self.options[self.hashGetter($viewValue)];
      };

      // Allow users to provide `ng-model="foo" ng-model-options="{trackBy: '$value.id'}"` so
      // that we can properly compare objects set on the model to the available options
      //
      // If the user doesn't provide a trackBy, we automatically generate an id for every
      // value passed in with the getId function
      if ($attrs.ngModelOptions) {
        self.hashGetter = function(value) {
          var ngModelOptions = $parse($attrs.ngModelOptions)($scope);
          var trackByOption = ngModelOptions && ngModelOptions.trackBy;

          if (trackByOption) {
            return $parse(trackByOption)($scope, { $value: value });
          } else if (angular.isObject(value)) {
            return getId(value);
          }
          return value;
        };
      } else {
        self.hashGetter = getId;
      }
      self.setMultiple(self.isMultiple);

      /**
       * If the value is an object, get the unique, incremental id of the value.
       * If it's not an object, the value will be converted to a string and then returned.
       * @param value
       * @returns {string}
       */
      function getId(value) {
        if (angular.isObject(value) && !angular.isArray(value)) {
          return 'object_' + (value.$$mdSelectId || (value.$$mdSelectId = ++selectNextId));
        }
        return value + '';
      }

      if (parentAttrs.hasOwnProperty('mdSelectOnlyOption')) {
        $mdUtil.nextTick(function() {
          var optionKeys = Object.keys(self.options);

          if (optionKeys.length === 1) {
            var option = self.options[optionKeys[0]];

            self.deselect(Object.keys(self.selected)[0]);
            self.select(self.hashGetter(option.value), option.value);
            self.refreshViewValue();
            self.ngModel.$setPristine();
          }
        }, false);
      }
    };

    /**
     * @param {string=} id
     */
    self.setActiveDescendant = function(id) {
      if (angular.isDefined(id)) {
        $element.find('md-content').attr('aria-activedescendant', id);
      } else {
        $element.find('md-content').removeAttr('aria-activedescendant');
      }
    };

    /**
     * @param {{mode: string}=} opts options object to allow specifying html (default) or aria mode.
     * @return {string} comma separated set of selected values
     */
    self.getSelectedLabels = function(opts) {
      opts = opts || {};
      var mode = opts.mode || 'html';
      var selectedOptionEls =
        $mdUtil.nodesToArray($element[0].querySelectorAll('md-option[selected]'));

      if (selectedOptionEls.length) {
        var mapFn;

        if (mode === 'html') {
          // Map the given element to its innerHTML string. If the element has a child ripple
          // container remove it from the HTML string, before returning the string.
          mapFn = function(el) {
            // If we do not have a `value` or `ng-value`, assume it is an empty option which clears
            // the select.
            if (el.hasAttribute('md-option-empty')) {
              return '';
            }

            var html = el.innerHTML;

            // Remove the ripple container from the selected option, copying it would cause a CSP
            // violation.
            var rippleContainer = el.querySelector('.md-ripple-container');
            if (rippleContainer) {
              html = html.replace(rippleContainer.outerHTML, '');
            }

            // Remove the checkbox container, because it will cause the label to wrap inside of the
            // placeholder. It should be not displayed inside of the label element.
            var checkboxContainer = el.querySelector('.md-container');
            if (checkboxContainer) {
              html = html.replace(checkboxContainer.outerHTML, '');
            }

            return html;
          };
        } else if (mode === 'aria') {
          mapFn = function(el) {
            return el.hasAttribute('aria-label') ? el.getAttribute('aria-label') : el.textContent;
          };
        }

        // Ensure there are no duplicates; see https://github.com/angular/material/issues/9442
        return $mdUtil.uniq(selectedOptionEls.map(mapFn)).join(', ');
      } else {
        return '';
      }
    };

    /**
     * Mark an option as selected
     * @param {string} hashKey key within the SelectMenuController.options object, which is an
     *  instance of OptionController.
     * @param {OptionController} hashedValue value to associate with the key
     */
    self.select = function(hashKey, hashedValue) {
      var option = self.options[hashKey];
      option && option.setSelected(true, self.isMultiple);
      self.selected[hashKey] = hashedValue;
    };

    /**
     * Mark an option as not selected
     * @param {string} hashKey key within the SelectMenuController.options object, which is an
     *  instance of OptionController.
     */
    self.deselect = function(hashKey) {
      var option = self.options[hashKey];
      option && option.setSelected(false, self.isMultiple);
      delete self.selected[hashKey];
    };

    /**
     * Add an option to the select
     * @param {string} hashKey key within the SelectMenuController.options object, which is an
     *  instance of OptionController.
     * @param {OptionController} optionCtrl instance to associate with the key
     */
    self.addOption = function(hashKey, optionCtrl) {
      if (angular.isDefined(self.options[hashKey])) {
        throw new Error('Duplicate md-option values are not allowed in a select. ' +
          'Duplicate value "' + optionCtrl.value + '" found.');
      }

      self.options[hashKey] = optionCtrl;

      // If this option's value was already in our ngModel, go ahead and select it.
      if (angular.isDefined(self.selected[hashKey])) {
        self.select(hashKey, optionCtrl.value);

        // When the current $modelValue of the ngModel Controller is using the same hash as
        // the current option, which will be added, then we can be sure, that the validation
        // of the option has occurred before the option was added properly.
        // This means, that we have to manually trigger a new validation of the current option.
        if (angular.isDefined(self.ngModel.$$rawModelValue) &&
            self.hashGetter(self.ngModel.$$rawModelValue) === hashKey) {
          self.ngModel.$validate();
        }

        self.refreshViewValue();
      }
    };

    /**
     * Remove an option from the select
     * @param {string} hashKey key within the SelectMenuController.options object, which is an
     *  instance of OptionController.
     */
    self.removeOption = function(hashKey) {
      delete self.options[hashKey];
      // Don't deselect an option when it's removed - the user's ngModel should be allowed
      // to have values that do not match a currently available option.
    };

    self.refreshViewValue = function() {
      var values = [];
      var option;
      for (var hashKey in self.selected) {
        // If this hashKey has an associated option, push that option's value to the model.
        if ((option = self.options[hashKey])) {
          values.push(option.value);
        } else {
          // Otherwise, the given hashKey has no associated option, and we got it
          // from an ngModel value at an earlier time. Push the unhashed value of
          // this hashKey to the model.
          // This allows the developer to put a value in the model that doesn't yet have
          // an associated option.
          values.push(self.selected[hashKey]);
        }
      }

      var newVal = self.isMultiple ? values : values[0];
      var prevVal = self.ngModel.$modelValue;

      if (!equals(prevVal, newVal)) {
        self.ngModel.$setViewValue(newVal);
        self.ngModel.$render();
      }

      function equals(prevVal, newVal) {
        if (self.isMultiple) {
          if (!angular.isArray(prevVal)) {
            // newVal is always an array when self.isMultiple is true
            // thus, if prevVal is not an array they are different
            return false;
          } else if (prevVal.length !== newVal.length) {
            // they are different if they have different length
            return false;
          } else {
            // if they have the same length, then they are different
            // if an item in the newVal array can't be found in the prevVal
            var prevValHashes = prevVal.map(function(prevValItem) {
              return self.hashGetter(prevValItem);
            });
            return newVal.every(function(newValItem) {
              var newValItemHash = self.hashGetter(newValItem);
              return prevValHashes.some(function(prevValHash) {
                return prevValHash === newValItemHash;
              });
            });
          }
        } else {
          return self.hashGetter(prevVal) === self.hashGetter(newVal);
        }
      }
    };

    /**
     * If the options include md-optgroups, then we need to apply aria-setsize and aria-posinset
     * to help screen readers understand the indexes. When md-optgroups are not used, we save on
     * perf and extra attributes by not applying these attributes as they are not needed by screen
     * readers.
     */
    function updateOptionSetSizeAndPosition() {
      var i, options;
      var hasOptGroup = $element.find('md-optgroup');
      if (!hasOptGroup.length) {
        return;
      }

      options = $element.find('md-option');

      for (i = 0; i < options.length; i++) {
        options[i].setAttribute('aria-setsize', options.length);
        options[i].setAttribute('aria-posinset', i + 1);
      }
    }

    function renderMultiple() {
      var newSelectedValues = self.ngModel.$modelValue || self.ngModel.$viewValue || [];
      if (!angular.isArray(newSelectedValues)) {
        return;
      }

      var oldSelected = Object.keys(self.selected);

      var newSelectedHashes = newSelectedValues.map(self.hashGetter);
      var deselected = oldSelected.filter(function(hash) {
        return newSelectedHashes.indexOf(hash) === -1;
      });

      deselected.forEach(self.deselect);
      newSelectedHashes.forEach(function(hashKey, i) {
        self.select(hashKey, newSelectedValues[i]);
      });
    }

    function renderSingular() {
      var value = self.ngModel.$viewValue || self.ngModel.$modelValue;
      Object.keys(self.selected).forEach(self.deselect);
      self.select(self.hashGetter(value), value);
    }
  }
}

/**
 * @ngdoc directive
 * @name mdOption
 * @restrict E
 * @module material.components.select
 *
 * @description Displays an option in a <a ng-href="api/directive/mdSelect">md-select</a> box's
 * dropdown menu. Options can be grouped using
 * <a ng-href="api/directive/mdOptgroup">md-optgroup</a> element directives.
 *
 * ### Option Params
 *
 * When applied, `md-option-empty` will mark the option as "empty" allowing the option to clear the
 * select and put it back in it's default state. You may supply this attribute on any option you
 * wish, however, it is automatically applied to an option whose `value` or `ng-value` are not
 * defined.
 *
 * **Automatically Applied**
 *
 *  - `<md-option>`
 *  - `<md-option value>`
 *  - `<md-option value="">`
 *  - `<md-option ng-value>`
 *  - `<md-option ng-value="">`
 *
 * **NOT Automatically Applied**
 *
 *  - `<md-option ng-value="1">`
 *  - `<md-option ng-value="''">`
 *  - `<md-option ng-value="undefined">`
 *  - `<md-option value="undefined">` (this evaluates to the string `"undefined"`)
 *  - <code ng-non-bindable>&lt;md-option ng-value="{{someValueThatMightBeUndefined}}"&gt;</code>
 *
 * **Note:** A value of `undefined` ***is considered a valid value*** (and does not auto-apply this
 * attribute) since you may wish this to be your "Not Available" or "None" option.
 *
 * **Note:** Using the
 * <a ng-href="https://developer.mozilla.org/en-US/docs/Web/HTML/Element/option#Attributes">value</a>
 * attribute from the `<option>` element (as opposed to the `<md-option>` element's
 * <a ng-href="https://docs.angularjs.org/api/ng/directive/ngValue">ng-value</a>) always evaluates
 * to a `string`. This means that `value="null"` will cause a check against `myValue != "null"`
 * rather than `!myValue` or `myValue != null`.
 * Importantly, this also applies to `number` values. `value="1"` will not match up with an
 * `ng-model` like `$scope.selectedValue = 1`. Use `ng-value="1"` in this case and other cases where
 * you have values that are not strings.
 *
 * **Note:** Please see our <a ng-href="api/directive/mdSelect#selects-and-object-equality">docs on
 * using objects with `md-select`</a> for additional guidance on using the `trackBy` option with
 * `ng-model-options`.
 *
 * @param {expression=} ng-value Binds the given expression to the value of the option.
 * @param {string=} value Attribute to set the value of the option.
 * @param {expression=} ng-repeat <a ng-href="https://docs.angularjs.org/api/ng/directive/ngRepeat">
 *  AngularJS directive</a> that instantiates a template once per item from a collection.
 * @param {expression=} ng-selected <a ng-href="https://docs.angularjs.org/api/ng/directive/ngSelected">
 *  AngularJS directive</a> that adds the `selected` attribute to the option when the expression
 *  evaluates as truthy.
 *
 *  **Note:** Unlike native `option` elements used with AngularJS, `md-option` elements
 *  watch their `selected` attributes for changes and trigger model value changes on `md-select`.
 * @param {boolean=} md-option-empty If the attribute exists, mark the option as "empty" allowing
 * the option to clear the select and put it back in it's default state. You may supply this
 * attribute on any option you wish, however, it is automatically applied to an option whose `value`
 * or `ng-value` are not defined.
 * @param {number=} tabindex The `tabindex` of the option. Defaults to `0`.
 *
 * @usage
 * <hljs lang="html">
 * <md-select ng-model="currentState" placeholder="Select a state">
 *   <md-option ng-value="AL">Alabama</md-option>
 *   <md-option ng-value="AK">Alaska</md-option>
 *   <md-option ng-value="FL">Florida</md-option>
 * </md-select>
 * </hljs>
 *
 * With `ng-repeat`:
 * <hljs lang="html">
 * <md-select ng-model="currentState" placeholder="Select a state">
 *   <md-option ng-value="state" ng-repeat="state in states">{{ state }}</md-option>
 * </md-select>
 * </hljs>
 */
function OptionDirective($mdButtonInkRipple, $mdUtil, $mdTheming) {

  return {
    restrict: 'E',
    require: ['mdOption', '^^mdSelectMenu'],
    controller: OptionController,
    compile: compile
  };

  function compile(element, attrs) {
    // Manual transclusion to avoid the extra inner <span> that ng-transclude generates
    element.append(angular.element('<div class="md-text">').append(element.contents()));

    element.attr('tabindex', attrs.tabindex || '0');

    if (!hasDefinedValue(attrs)) {
      element.attr('md-option-empty', '');
    }

    return postLink;
  }

  /**
   * @param {Object} attrs list of attributes from the compile function
   * @return {string|undefined|null} if defined and non-empty, return the value of the option's
   *  value attribute, otherwise return the value of the option's ng-value attribute.
   */
  function hasDefinedValue(attrs) {
    var value = attrs.value;
    var ngValue = attrs.ngValue;

    return value || ngValue;
  }

  function postLink(scope, element, attrs, ctrls) {
    var optionCtrl = ctrls[0];
    var selectMenuCtrl = ctrls[1];

    $mdTheming(element);

    if (selectMenuCtrl.isMultiple) {
      element.addClass('md-checkbox-enabled');
      element.prepend(CHECKBOX_SELECTION_INDICATOR.clone());
    }

    if (angular.isDefined(attrs.ngValue)) {
      scope.$watch(attrs.ngValue, function (newValue, oldValue) {
        setOptionValue(newValue, oldValue);
        element.removeAttr('aria-checked');
      });
    } else if (angular.isDefined(attrs.value)) {
      setOptionValue(attrs.value);
    } else {
      scope.$watch(function() {
        return element.text().trim();
      }, setOptionValue);
    }

    attrs.$observe('disabled', function(disabled) {
      if (disabled) {
        element.attr('tabindex', '-1');
      } else {
        element.attr('tabindex', '0');
      }
    });

    scope.$$postDigest(function() {
      attrs.$observe('selected', function(selected) {
        if (!angular.isDefined(selected)) return;
        if (typeof selected == 'string') selected = true;
        if (selected) {
          if (!selectMenuCtrl.isMultiple) {
            selectMenuCtrl.deselect(Object.keys(selectMenuCtrl.selected)[0]);
          }
          selectMenuCtrl.select(optionCtrl.hashKey, optionCtrl.value);
        } else {
          selectMenuCtrl.deselect(optionCtrl.hashKey);
        }
        selectMenuCtrl.refreshViewValue();
      });
    });

    $mdButtonInkRipple.attach(scope, element);
    configureAria();

    /**
     * @param {*} newValue the option's new value
     * @param {*=} oldValue the option's previous value
     * @param {boolean=} prevAttempt true if this had to be attempted again due to an undefined
     *  hashGetter on the selectMenuCtrl, undefined otherwise.
     */
    function setOptionValue(newValue, oldValue, prevAttempt) {
      if (!selectMenuCtrl.hashGetter) {
        if (!prevAttempt) {
          scope.$$postDigest(function() {
            setOptionValue(newValue, oldValue, true);
          });
        }
        return;
      }
      var oldHashKey = selectMenuCtrl.hashGetter(oldValue, scope);
      var newHashKey = selectMenuCtrl.hashGetter(newValue, scope);

      optionCtrl.hashKey = newHashKey;
      optionCtrl.value = newValue;

      selectMenuCtrl.removeOption(oldHashKey, optionCtrl);
      selectMenuCtrl.addOption(newHashKey, optionCtrl);
    }

    scope.$on('$destroy', function() {
      selectMenuCtrl.removeOption(optionCtrl.hashKey, optionCtrl);
    });

    function configureAria() {
      var ariaAttrs = {
        'role': 'option'
      };

      // We explicitly omit the `aria-selected` attribute from single-selection, unselected
      // options. Including the `aria-selected="false"` attributes adds a significant amount of
      // noise to screen-reader users without providing useful information.
      if (selectMenuCtrl.isMultiple) {
        ariaAttrs['aria-selected'] = 'false';
      }

      if (!element[0].hasAttribute('id')) {
        ariaAttrs.id = 'select_option_' + $mdUtil.nextUid();
      }
      element.attr(ariaAttrs);
    }
  }
}

function OptionController($element) {
  /**
   * @param {boolean} isSelected
   * @param {boolean=} isMultiple
   */
  this.setSelected = function(isSelected, isMultiple) {
    if (isSelected) {
      $element.attr({
        'selected': 'true',
        'aria-selected': 'true'
      });
    } else if (!isSelected) {
      $element.removeAttr('selected');

      if (isMultiple) {
        $element.attr('aria-selected', 'false');
      } else {
        // We explicitly omit the `aria-selected` attribute from single-selection, unselected
        // options. Including the `aria-selected="false"` attributes adds a significant amount of
        // noise to screen-reader users without providing useful information.
        $element.removeAttr('aria-selected');
      }
    }
  };
}

/**
 * @ngdoc directive
 * @name mdOptgroup
 * @restrict E
 * @module material.components.select
 *
 * @description Displays a label separating groups of
 * <a ng-href="api/directive/mdOption">md-option</a> element directives in a
 * <a ng-href="api/directive/mdSelect">md-select</a> box's dropdown menu.
 *
 * **Note:** When using `md-select-header` element directives within a `md-select`, the labels that
 * would normally be added to the <a ng-href="api/directive/mdOptgroup">md-optgroup</a> directives
 * are omitted, allowing the `md-select-header` to represent the option group label
 * (and possibly more).
 *
 * @usage
 * With label attributes
 * <hljs lang="html">
 * <md-select ng-model="currentState" placeholder="Select a state">
 *   <md-optgroup label="Southern">
 *     <md-option ng-value="AL">Alabama</md-option>
 *     <md-option ng-value="FL">Florida</md-option>
 *   </md-optgroup>
 *   <md-optgroup label="Northern">
 *     <md-option ng-value="AK">Alaska</md-option>
 *     <md-option ng-value="MA">Massachusetts</md-option>
 *   </md-optgroup>
 * </md-select>
 * </hljs>
 *
 * With label elements
 * <hljs lang="html">
 * <md-select ng-model="currentState" placeholder="Select a state">
 *   <md-optgroup>
 *     <label>Southern</label>
 *     <md-option ng-value="AL">Alabama</md-option>
 *     <md-option ng-value="FL">Florida</md-option>
 *   </md-optgroup>
 *   <md-optgroup>
 *     <label>Northern</label>
 *     <md-option ng-value="AK">Alaska</md-option>
 *     <md-option ng-value="MA">Massachusetts</md-option>
 *   </md-optgroup>
 * </md-select>
 * </hljs>
 *
 * @param {string=} label The option group's label.
 */
function OptgroupDirective() {
  return {
    restrict: 'E',
    compile: compile
  };
  function compile(element, attrs) {
    // If we have a select header element, we don't want to add the normal label
    // header.
    if (!hasSelectHeader()) {
      setupLabelElement();
    }
    element.attr('role', 'group');

    function hasSelectHeader() {
      return element.parent().find('md-select-header').length;
    }

    function setupLabelElement() {
      var labelElement = element.find('label');
      if (!labelElement.length) {
        labelElement = angular.element('<label>');
        element.prepend(labelElement);
      }
      labelElement.addClass('md-container-ignore');
      labelElement.attr('aria-hidden', 'true');
      if (attrs.label) {
        labelElement.text(attrs.label);
      }
      element.attr('aria-label', labelElement.text());
    }
  }
}

function SelectHeaderDirective() {
  return {
    restrict: 'E',
  };
}

function SelectProvider($$interimElementProvider) {
  selectDefaultOptions.$inject = ["$mdSelect", "$mdConstant", "$mdUtil", "$window", "$q", "$$rAF", "$animateCss", "$animate", "$document"];
  return $$interimElementProvider('$mdSelect')
    .setDefaults({
      methods: ['target'],
      options: selectDefaultOptions
    });

  /* @ngInject */
  function selectDefaultOptions($mdSelect, $mdConstant, $mdUtil, $window, $q, $$rAF, $animateCss, $animate, $document) {
    var ERROR_TARGET_EXPECTED = "$mdSelect.show() expected a target element in options.target but got '{0}'!";
    var animator = $mdUtil.dom.animator;
    var keyCodes = $mdConstant.KEY_CODE;

    return {
      parent: 'body',
      themable: true,
      onShow: onShow,
      onRemove: onRemove,
      hasBackdrop: true,
      disableParentScroll: true
    };

    /**
     * Interim-element onRemove logic....
     */
    function onRemove(scope, element, opts) {
      var animationRunner = null;
      var destroyListener = scope.$on('$destroy', function() {
        // Listen for the case where the element was destroyed while there was an
        // ongoing close animation. If this happens, we need to end the animation
        // manually.
        animationRunner.end();
      });

      opts = opts || { };
      opts.cleanupInteraction();
      opts.cleanupResizing();
      opts.hideBackdrop();

      // For navigation $destroy events, do a quick, non-animated removal,
      // but for normal closes (from clicks, etc) animate the removal
      return (opts.$destroy === true) ? cleanElement() : animateRemoval().then(cleanElement);

      /**
       * For normal closes (eg clicks), animate the removal.
       * For forced closes (like $destroy events from navigation),
       * skip the animations.
       */
      function animateRemoval() {
        animationRunner = $animateCss(element, {addClass: 'md-leave'});
        return animationRunner.start();
      }

      /**
       * Restore the element to a closed state
       */
      function cleanElement() {
        destroyListener();

        element
          .removeClass('md-active')
          .attr('aria-hidden', 'true')
          .css({
            'display': 'none',
            'top': '',
            'right': '',
            'bottom': '',
            'left': '',
            'font-size': '',
            'min-width': ''
          });

        announceClosed(opts);

        if (!opts.$destroy) {
          if (opts.restoreFocus) {
            opts.target.focus();
          } else {
            // Make sure that the container's md-input-focused is removed on backdrop click.
            $mdUtil.nextTick(function() {
              opts.target.triggerHandler('blur');
            }, true);
          }
        }
      }
    }

    /**
     * Interim-element onShow logic.
     */
    function onShow(scope, element, opts) {

      watchAsyncLoad();
      sanitizeAndConfigure(scope, opts);

      opts.hideBackdrop = showBackdrop(scope, element, opts);

      return showDropDown(scope, element, opts)
        .then(function(response) {
          element.attr('aria-hidden', 'false');
          opts.alreadyOpen = true;
          opts.cleanupInteraction = activateInteraction();
          opts.cleanupResizing = activateResizing();
          opts.contentEl[0].focus();

          return response;
        }, opts.hideBackdrop);

      // ************************************
      // Closure Functions
      // ************************************

      /**
       * Attach the select DOM element(s) and animate to the correct positions and scale.
       */
      function showDropDown(scope, element, opts) {
        if (opts.parent !== element.parent()) {
          element.parent().attr('aria-owns', element.find('md-content').attr('id'));
        }

        opts.parent.append(element);

        return $q(function(resolve, reject) {
          try {
            $animateCss(element, {removeClass: 'md-leave', duration: 0})
              .start()
              .then(positionAndFocusMenu)
              .then(resolve);

          } catch (e) {
            reject(e);
          }
        });
      }

      /**
       * Initialize container and dropDown menu positions/scale, then animate to show.
       * @return {*} a Promise that resolves after the menu is animated in and an item is focused
       */
      function positionAndFocusMenu() {
        return $q(function(resolve) {
          if (opts.isRemoved) return $q.reject(false);

          var info = calculateMenuPositions(scope, element, opts);

          info.container.element.css(animator.toCss(info.container.styles));
          info.dropDown.element.css(animator.toCss(info.dropDown.styles));

          $$rAF(function() {
            element.addClass('md-active');
            info.dropDown.element.css(animator.toCss({transform: ''}));
            autoFocus(opts.focusedNode);

            resolve();
          });

        });
      }

      /**
       * Show modal backdrop element.
       */
      function showBackdrop(scope, element, options) {

        // If we are not within a dialog...
        if (options.disableParentScroll && !$mdUtil.getClosest(options.target, 'MD-DIALOG')) {
          // !! DO this before creating the backdrop; since disableScrollAround()
          //    configures the scroll offset; which is used by mdBackDrop postLink()
          options.restoreScroll = $mdUtil.disableScrollAround(options.element, options.parent);
        } else {
          options.disableParentScroll = false;
        }

        if (options.hasBackdrop) {
          // Override duration to immediately show invisible backdrop
          options.backdrop = $mdUtil.createBackdrop(scope, "md-select-backdrop md-click-catcher");
          $animate.enter(options.backdrop, $document[0].body, null, {duration: 0});
        }

        /**
         * Hide modal backdrop element...
         */
        return function hideBackdrop() {
          if (options.backdrop) options.backdrop.remove();
          if (options.disableParentScroll) options.restoreScroll();

          delete options.restoreScroll;
        };
      }

      /**
       * @param {Element|HTMLElement|null=} previousNode
       * @param {Element|HTMLElement} node
       * @param {SelectMenuController|Function|object=} menuController SelectMenuController instance
       */
      function focusOptionNode(previousNode, node, menuController) {
        var listboxContentNode = opts.contentEl[0];

        if (node) {
          if (previousNode) {
            previousNode.classList.remove('md-focused');
          }

          node.classList.add('md-focused');
          if (menuController && menuController.setActiveDescendant) {
            menuController.setActiveDescendant(node.id);
          }

          // Scroll the node into view if needed.
          if (listboxContentNode.scrollHeight > listboxContentNode.clientHeight) {
            var scrollBottom = listboxContentNode.clientHeight + listboxContentNode.scrollTop;
            var nodeBottom = node.offsetTop + node.offsetHeight;
            if (nodeBottom > scrollBottom) {
              listboxContentNode.scrollTop = nodeBottom - listboxContentNode.clientHeight;
            } else if (node.offsetTop < listboxContentNode.scrollTop) {
              listboxContentNode.scrollTop = node.offsetTop;
            }
          }
          opts.focusedNode = node;
          if (menuController && menuController.refreshViewValue) {
            menuController.refreshViewValue();
          }
        }
      }

      /**
       * @param {Element|HTMLElement} nodeToFocus
       */
      function autoFocus(nodeToFocus) {
        var selectMenuController;
        if (nodeToFocus && !nodeToFocus.hasAttribute('disabled')) {
          selectMenuController = opts.selectEl.controller('mdSelectMenu');
          focusOptionNode(null, nodeToFocus, selectMenuController);
        }
      }

      /**
       * Check for valid opts and set some sane defaults
       */
      function sanitizeAndConfigure(scope, options) {
        var selectMenuElement = element.find('md-select-menu');

        if (!options.target) {
          throw new Error($mdUtil.supplant(ERROR_TARGET_EXPECTED, [options.target]));
        }

        angular.extend(options, {
          isRemoved: false,
          target: angular.element(options.target), // make sure it's not a naked DOM node
          parent: angular.element(options.parent),
          selectEl: selectMenuElement,
          contentEl: element.find('md-content'),
          optionNodes: selectMenuElement[0].getElementsByTagName('md-option')
        });
      }

      /**
       * Configure various resize listeners for screen changes
       */
      function activateResizing() {
        var debouncedOnResize = (function(scope, target, options) {

          return function() {
            if (options.isRemoved) return;

            var updates = calculateMenuPositions(scope, target, options);
            var container = updates.container;
            var dropDown = updates.dropDown;

            container.element.css(animator.toCss(container.styles));
            dropDown.element.css(animator.toCss(dropDown.styles));
          };

        })(scope, element, opts);

        var window = angular.element($window);
        window.on('resize', debouncedOnResize);
        window.on('orientationchange', debouncedOnResize);

        // Publish deactivation closure...
        return function deactivateResizing() {

          // Disable resizing handlers
          window.off('resize', debouncedOnResize);
          window.off('orientationchange', debouncedOnResize);
        };
      }

      /**
       * If asynchronously loading, watch and update internal '$$loadingAsyncDone' flag.
       */
      function watchAsyncLoad() {
        if (opts.loadingAsync && !opts.isRemoved) {
          scope.$$loadingAsyncDone = false;

          $q.when(opts.loadingAsync)
            .then(function() {
              scope.$$loadingAsyncDone = true;
              delete opts.loadingAsync;
            }).then(function() {
              $$rAF(positionAndFocusMenu);
            });
        }
      }

      function activateInteraction() {
        if (opts.isRemoved) {
          return;
        }

        var dropDown = opts.selectEl;
        var selectMenuController = dropDown.controller('mdSelectMenu') || {};
        var listbox = opts.contentEl;

        element.addClass('md-clickable');

        // Close on backdrop click
        opts.backdrop && opts.backdrop.on('click', onBackdropClick);

        // Escape to close
        // Cycling of options, and closing on enter
        dropDown.on('keydown', onMenuKeyDown);
        dropDown.on('click', checkCloseMenu);

        return function cleanupInteraction() {
          opts.backdrop && opts.backdrop.off('click', onBackdropClick);
          dropDown.off('keydown', onMenuKeyDown);
          dropDown.off('click', checkCloseMenu);

          element.removeClass('md-clickable');
          opts.isRemoved = true;
        };

        // ************************************
        // Closure Functions
        // ************************************

        function onBackdropClick(e) {
          e.preventDefault();
          e.stopPropagation();
          opts.restoreFocus = false;
          $mdUtil.nextTick($mdSelect.hide, true);
        }

        function onMenuKeyDown(ev) {
          ev.preventDefault();
          ev.stopPropagation();

          switch (ev.keyCode) {
            case keyCodes.UP_ARROW:
              return focusPrevOption();
            case keyCodes.DOWN_ARROW:
              return focusNextOption();
            case keyCodes.SPACE:
            case keyCodes.ENTER:
              if (opts.focusedNode) {
                dropDown.triggerHandler({
                  type: 'click',
                  target: opts.focusedNode
                });
                ev.preventDefault();
              }
              checkCloseMenu(ev);
              break;
            case keyCodes.TAB:
            case keyCodes.ESCAPE:
              ev.stopPropagation();
              ev.preventDefault();
              opts.restoreFocus = true;
              $mdUtil.nextTick($mdSelect.hide, true);
              break;
            default:
              if (shouldHandleKey(ev, $mdConstant)) {
                var optNode = selectMenuController.optNodeForKeyboardSearch(ev);
                if (optNode && !optNode.hasAttribute('disabled')) {
                  focusOptionNode(opts.focusedNode, optNode, selectMenuController);
                }
              }
          }
        }

        /**
         * Change the focus to another option. If there is no focused option, focus the first
         * option. If there is a focused option, then use the direction to determine if we should
         * focus the previous or next option in the list.
         * @param {'next'|'prev'} direction
         */
        function focusOption(direction) {
          var optionsArray = $mdUtil.nodesToArray(opts.optionNodes);
          var index = optionsArray.indexOf(opts.focusedNode);
          var prevOption = optionsArray[index];
          var newOption;

          do {
            if (index === -1) {
              // We lost the previously focused element, reset to first option
              index = 0;
            } else if (direction === 'next' && index < optionsArray.length - 1) {
              index++;
            } else if (direction === 'prev' && index > 0) {
              index--;
            }
            newOption = optionsArray[index];
            if (newOption.hasAttribute('disabled')) {
              newOption = null;
            }
          } while (!newOption && index < optionsArray.length - 1 && index > 0);

          focusOptionNode(prevOption, newOption, selectMenuController);
        }

        function focusNextOption() {
          focusOption('next');
        }

        function focusPrevOption() {
          focusOption('prev');
        }

        /**
         * @param {KeyboardEvent|MouseEvent} event
         */
        function checkCloseMenu(event) {
          if (event && (event.type === 'click') && (event.currentTarget !== dropDown[0])) {
            return;
          }
          if (mouseOnScrollbar()) {
            return;
          }

          if (opts.focusedNode && opts.focusedNode.hasAttribute &&
              !opts.focusedNode.hasAttribute('disabled')) {
            event.preventDefault();
            event.stopPropagation();
            if (!selectMenuController.isMultiple) {
              opts.restoreFocus = true;

              $mdUtil.nextTick(function () {
                $mdSelect.hide(selectMenuController.ngModel.$viewValue);
                opts.focusedNode.classList.remove('md-focused');
              }, true);
            }
          }

          /**
           * check if the mouseup event was on a scrollbar
           */
          function mouseOnScrollbar() {
            var clickOnScrollbar = false;
            if (event && (event.currentTarget.children.length > 0)) {
              var child = event.currentTarget.children[0];
              var hasScrollbar = child.scrollHeight > child.clientHeight;
              if (hasScrollbar && child.children.length > 0) {
                var relPosX = event.pageX - event.currentTarget.getBoundingClientRect().left;
                if (relPosX > child.querySelector('md-option').offsetWidth)
                  clickOnScrollbar = true;
              }
            }
            return clickOnScrollbar;
          }
        }
      }
    }

    /**
     * To notify listeners that the Select menu has closed,
     * trigger the [optional] user-defined expression
     */
    function announceClosed(opts) {
      var mdSelect = opts.selectCtrl;
      if (mdSelect) {
        var menuController = opts.selectEl.controller('mdSelectMenu');
        mdSelect.setSelectValueText(menuController ? menuController.getSelectedLabels() : '');
        mdSelect.triggerClose();
      }
    }


    /**
     * Calculate the menu positions after an event like options changing, screen resizing, or
     * animations finishing.
     * @param {Object} scope
     * @param element
     * @param opts
     * @return {{container: {styles: {top: number, left: number, 'font-size': *, 'min-width': number}, element: Object}, dropDown: {styles: {transform: string, transformOrigin: string}, element: Object}}}
     */
    function calculateMenuPositions(scope, element, opts) {
      var
        containerNode = element[0],
        targetNode = opts.target[0].children[0], // target the label
        parentNode = $document[0].body,
        selectNode = opts.selectEl[0],
        contentNode = opts.contentEl[0],
        parentRect = parentNode.getBoundingClientRect(),
        targetRect = targetNode.getBoundingClientRect(),
        shouldOpenAroundTarget = false,
        bounds = {
          left: parentRect.left + SELECT_EDGE_MARGIN,
          top: SELECT_EDGE_MARGIN,
          bottom: parentRect.height - SELECT_EDGE_MARGIN,
          right: parentRect.width - SELECT_EDGE_MARGIN - ($mdUtil.floatingScrollbars() ? 16 : 0)
        },
        spaceAvailable = {
          top: targetRect.top - bounds.top,
          left: targetRect.left - bounds.left,
          right: bounds.right - (targetRect.left + targetRect.width),
          bottom: bounds.bottom - (targetRect.top + targetRect.height)
        },
        maxWidth = parentRect.width - SELECT_EDGE_MARGIN * 2,
        selectedNode = selectNode.querySelector('md-option[selected]'),
        optionNodes = selectNode.getElementsByTagName('md-option'),
        optgroupNodes = selectNode.getElementsByTagName('md-optgroup'),
        isScrollable = calculateScrollable(element, contentNode),
        centeredNode;

      var loading = isPromiseLike(opts.loadingAsync);
      if (!loading) {
        // If a selected node, center around that
        if (selectedNode) {
          centeredNode = selectedNode;
          // If there are option groups, center around the first option group
        } else if (optgroupNodes.length) {
          centeredNode = optgroupNodes[0];
          // Otherwise - if we are not loading async - center around the first optionNode
        } else if (optionNodes.length) {
          centeredNode = optionNodes[0];
          // In case there are no options, center on whatever's in there... (eg progress indicator)
        } else {
          centeredNode = contentNode.firstElementChild || contentNode;
        }
      } else {
        // If loading, center on progress indicator
        centeredNode = contentNode.firstElementChild || contentNode;
      }

      if (contentNode.offsetWidth > maxWidth) {
        contentNode.style['max-width'] = maxWidth + 'px';
      } else {
        contentNode.style.maxWidth = null;
      }
      if (shouldOpenAroundTarget) {
        contentNode.style['min-width'] = targetRect.width + 'px';
      }

      // Remove padding before we compute the position of the menu
      if (isScrollable) {
        selectNode.classList.add('md-overflow');
      }

      var focusedNode = centeredNode;
      if ((focusedNode.tagName || '').toUpperCase() === 'MD-OPTGROUP') {
        focusedNode = optionNodes[0] || contentNode.firstElementChild || contentNode;
        centeredNode = focusedNode;
      }
      // Cache for autoFocus()
      opts.focusedNode = focusedNode;

      // Get the selectMenuRect *after* max-width is possibly set above
      containerNode.style.display = 'block';
      var selectMenuRect = selectNode.getBoundingClientRect();
      var centeredRect = getOffsetRect(centeredNode);

      if (centeredNode) {
        var centeredStyle = $window.getComputedStyle(centeredNode);
        centeredRect.paddingLeft = parseInt(centeredStyle.paddingLeft, 10) || 0;
        centeredRect.paddingRight = parseInt(centeredStyle.paddingRight, 10) || 0;
      }

      if (isScrollable) {
        var scrollBuffer = contentNode.offsetHeight / 2;
        contentNode.scrollTop = centeredRect.top + centeredRect.height / 2 - scrollBuffer;

        if (spaceAvailable.top < scrollBuffer) {
          contentNode.scrollTop = Math.min(
            centeredRect.top,
            contentNode.scrollTop + scrollBuffer - spaceAvailable.top
          );
        } else if (spaceAvailable.bottom < scrollBuffer) {
          contentNode.scrollTop = Math.max(
            centeredRect.top + centeredRect.height - selectMenuRect.height,
            contentNode.scrollTop - scrollBuffer + spaceAvailable.bottom
          );
        }
      }

      var left, top, transformOrigin, minWidth, fontSize;
      if (shouldOpenAroundTarget) {
        left = targetRect.left;
        top = targetRect.top + targetRect.height;
        transformOrigin = '50% 0';
        if (top + selectMenuRect.height > bounds.bottom) {
          top = targetRect.top - selectMenuRect.height;
          transformOrigin = '50% 100%';
        }
      } else {
        left = (targetRect.left + centeredRect.left - centeredRect.paddingLeft) + 2;
        top = Math.floor(targetRect.top + targetRect.height / 2 - centeredRect.height / 2 -
            centeredRect.top + contentNode.scrollTop) + 2;

        transformOrigin = (centeredRect.left + targetRect.width / 2) + 'px ' +
          (centeredRect.top + centeredRect.height / 2 - contentNode.scrollTop) + 'px 0px';

        minWidth = Math.min(targetRect.width + centeredRect.paddingLeft + centeredRect.paddingRight, maxWidth);

        fontSize = window.getComputedStyle(targetNode)['font-size'];
      }

      // Keep left and top within the window
      var containerRect = containerNode.getBoundingClientRect();
      var scaleX = Math.round(100 * Math.min(targetRect.width / selectMenuRect.width, 1.0)) / 100;
      var scaleY = Math.round(100 * Math.min(targetRect.height / selectMenuRect.height, 1.0)) / 100;

      return {
        container: {
          element: angular.element(containerNode),
          styles: {
            left: Math.floor(clamp(bounds.left, left, bounds.right - minWidth)),
            top: Math.floor(clamp(bounds.top, top, bounds.bottom - containerRect.height)),
            'min-width': minWidth,
            'font-size': fontSize
          }
        },
        dropDown: {
          element: angular.element(selectNode),
          styles: {
            transformOrigin: transformOrigin,
            transform: !opts.alreadyOpen ? $mdUtil.supplant('scale({0},{1})', [scaleX, scaleY]) : ""
          }
        }
      };
    }
  }

  function isPromiseLike(obj) {
    return obj && angular.isFunction(obj.then);
  }

  function clamp(min, n, max) {
    return Math.max(min, Math.min(n, max));
  }

  function getOffsetRect(node) {
    return node ? {
      left: node.offsetLeft,
      top: node.offsetTop,
      width: node.offsetWidth,
      height: node.offsetHeight
    } : {left: 0, top: 0, width: 0, height: 0};
  }

  function calculateScrollable(element, contentNode) {
    var isScrollable = false;

    try {
      var oldDisplay = element[0].style.display;

      // Set the element's display to block so that this calculation is correct
      element[0].style.display = 'block';

      isScrollable = contentNode.scrollHeight > contentNode.offsetHeight;

      // Reset it back afterwards
      element[0].style.display = oldDisplay;
    } finally {
      // Nothing to do
    }
    return isScrollable;
  }
}

function shouldHandleKey(ev, $mdConstant) {
  var char = String.fromCharCode(ev.keyCode);
  var isNonUsefulKey = (ev.keyCode <= 31);

  return (char && char.length && !isNonUsefulKey &&
    !$mdConstant.isMetaKey(ev) && !$mdConstant.isFnLockKey(ev) && !$mdConstant.hasModifierKey(ev));
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.showHide
 */

// Add additional handlers to ng-show and ng-hide that notify directives
// contained within that they should recompute their size.
// These run in addition to AngularJS's built-in ng-hide and ng-show directives.
angular.module('material.components.showHide', [
  'material.core'
])
  .directive('ngShow', createDirective('ngShow', true))
  .directive('ngHide', createDirective('ngHide', false));


function createDirective(name, targetValue) {
  return ['$mdUtil', '$window', function($mdUtil, $window) {
    return {
      restrict: 'A',
      multiElement: true,
      link: function($scope, $element, $attr) {
        var unregister = $scope.$on('$md-resize-enable', function() {
          unregister();

          var node = $element[0];
          var cachedTransitionStyles = node.nodeType === $window.Node.ELEMENT_NODE ?
            $window.getComputedStyle(node) : {};

          $scope.$watch($attr[name], function(value) {
            if (!!value === targetValue) {
              $mdUtil.nextTick(function() {
                $scope.$broadcast('$md-resize');
              });

              var opts = {
                cachedTransitionStyles: cachedTransitionStyles
              };

              $mdUtil.dom.animator.waitTransitionEnd($element, opts).then(function() {
                $scope.$broadcast('$md-resize');
              });
            }
          });
        });
      }
    };
  }];
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.sidenav
 *
 * @description
 * A Sidenav component.
 */
SidenavService.$inject = ["$mdComponentRegistry", "$mdUtil", "$q", "$log"];
SidenavDirective.$inject = ["$mdMedia", "$mdUtil", "$mdConstant", "$mdTheming", "$mdInteraction", "$animate", "$compile", "$parse", "$log", "$q", "$document", "$window", "$$rAF"];
SidenavController.$inject = ["$scope", "$attrs", "$mdComponentRegistry", "$q", "$interpolate"];
angular
  .module('material.components.sidenav', [
    'material.core',
    'material.components.backdrop'
  ])
  .factory('$mdSidenav', SidenavService)
  .directive('mdSidenav', SidenavDirective)
  .directive('mdSidenavFocus', SidenavFocusDirective)
  .controller('$mdSidenavController', SidenavController);


/**
 * @ngdoc service
 * @name $mdSidenav
 * @module material.components.sidenav
 *
 * @description
 * `$mdSidenav` makes it easy to interact with multiple sidenavs in an app. When looking up a
 * sidenav instance, you can either look it up synchronously or wait for it to be initialized
 * asynchronously. This is done by passing the second argument to `$mdSidenav`.
 *
 * @usage
 * <hljs lang="js">
 * // Async lookup for sidenav instance; will resolve when the instance is available
 * $mdSidenav(componentId, true).then(function(instance) {
 *   $log.debug( componentId + "is now ready" );
 * });
 * // Sync lookup for sidenav instance; this will resolve immediately.
 * $mdSidenav(componentId).then(function(instance) {
 *   $log.debug( componentId + "is now ready" );
 * });
 * // Async toggle the given sidenav;
 * // when instance is known ready and lazy lookup is not needed.
 * $mdSidenav(componentId)
 *    .toggle()
 *    .then(function(){
 *      $log.debug('toggled');
 *    });
 * // Async open the given sidenav
 * $mdSidenav(componentId)
 *    .open()
 *    .then(function(){
 *      $log.debug('opened');
 *    });
 * // Async close the given sidenav
 * $mdSidenav(componentId)
 *    .close()
 *    .then(function(){
 *      $log.debug('closed');
 *    });
 * // Async lookup for sidenav instance
 * $mdSidenav(componentId, true).then(function(instance) {
 *   // On close callback to handle close, backdrop click, or escape key pressed.
 *   // Callback happens BEFORE the close action occurs.
 *   instance.onClose(function() {
 *     $log.debug('closing');
 *   });
 * });
 * // Sync check to see if the specified sidenav is set to be open
 * $mdSidenav(componentId).isOpen();
 * // Sync check to whether given sidenav is locked open
 * // If this is true, the sidenav will be open regardless of close()
 * $mdSidenav(componentId).isLockedOpen();
 * </hljs>
 */
function SidenavService($mdComponentRegistry, $mdUtil, $q, $log) {
  var errorMsg = "SideNav '{0}' is not available! Did you use md-component-id='{0}'?";
  var service = {
    find: findInstance,      //  sync  - returns proxy API
    waitFor: waitForInstance //  async - returns promise
  };

  /**
   * Service API that supports three (3) usages:
   * $mdSidenav().find("left")               // sync (must already exist) or returns undefined
   * $mdSidenav("left").toggle();            // sync (must already exist) or returns reject promise;
   * $mdSidenav("left",true).then(function(left) { // async returns instance when available
   *  left.toggle();
   * });
   */
  return function(handle, enableWait) {
    if (angular.isUndefined(handle)) {
      return service;
    }

    var shouldWait = enableWait === true;
    var instance = service.find(handle, shouldWait);
    return !instance && shouldWait ? service.waitFor(handle) :
           !instance && angular.isUndefined(enableWait) ? addLegacyAPI(service, handle) : instance;
  };

  /**
   * For failed instance/handle lookups, older-clients expect an response object with noops
   * that include `rejected promise APIs`
   * @param service
   * @param handle
   * @returns {Object}
   */
  function addLegacyAPI(service, handle) {
    var falseFn = function() {
      return false;
    };
    var rejectFn = function() {
      return $q.when($mdUtil.supplant(errorMsg, [handle || ""]));
    };

    return angular.extend({
      isLockedOpen: falseFn,
      isOpen: falseFn,
      toggle: rejectFn,
      open: rejectFn,
      close: rejectFn,
      onClose: angular.noop,
      then: function(callback) {
        return waitForInstance(handle).then(callback || angular.noop);
      }
    }, service);
  }

  /**
   * Synchronously lookup the controller instance for the specified sidNav instance which has been
   * registered with the markup `md-component-id`
   */
  function findInstance(handle, shouldWait) {
    var instance = $mdComponentRegistry.get(handle);

    if (!instance && !shouldWait) {
      // Report missing instance
      $log.error($mdUtil.supplant(errorMsg, [handle || ""]));

      // The component has not registered itself... most like NOT yet created
      // return null to indicate that the Sidenav is not in the DOM
      return undefined;
    }
    return instance;
  }

  /**
   * Asynchronously wait for the component instantiation,
   * Deferred lookup of component instance using $component registry
   */
  function waitForInstance(handle) {
    return $mdComponentRegistry.when(handle).catch($log.error);
  }
}

/**
 * @ngdoc directive
 * @name mdSidenavFocus
 * @module material.components.sidenav
 *
 * @restrict A
 *
 * @description
 * `mdSidenavFocus` provides a way to specify the focused element when a sidenav opens.
 * This is completely optional, as the sidenav itself is focused by default.
 *
 * @usage
 * <hljs lang="html">
 * <md-sidenav>
 *   <form>
 *     <md-input-container>
 *       <label for="testInput">Label</label>
 *       <input id="testInput" type="text" md-sidenav-focus>
 *     </md-input-container>
 *   </form>
 * </md-sidenav>
 * </hljs>
 **/
function SidenavFocusDirective() {
  return {
    restrict: 'A',
    require: '^mdSidenav',
    link: function(scope, element, attr, sidenavCtrl) {
      // @see $mdUtil.findFocusTarget(...)
    }
  };
}

/**
 * @ngdoc directive
 * @name mdSidenav
 * @module material.components.sidenav
 * @restrict E
 *
 * @description
 * A Sidenav component that can be opened and closed programmatically.
 *
 * By default, upon opening it will slide out on top of the main content area.
 *
 * For keyboard and screen reader accessibility, focus is sent to the sidenav wrapper by default.
 * It can be overridden with the `md-autofocus` directive on the child element you want focused.
 *
 * @usage
 * <hljs lang="html">
 * <div layout="row" ng-controller="MyController">
 *   <md-sidenav md-component-id="left" class="md-sidenav-left">
 *     Left Nav!
 *   </md-sidenav>
 *
 *   <md-content>
 *     Center Content
 *     <md-button ng-click="openLeftMenu()">
 *       Open Left Menu
 *     </md-button>
 *   </md-content>
 *
 *   <md-sidenav md-component-id="right"
 *     md-is-locked-open="$mdMedia('min-width: 333px')"
 *     class="md-sidenav-right">
 *     <form>
 *       <md-input-container>
 *         <label for="testInput">Test input</label>
 *         <input id="testInput" type="text"
 *                ng-model="data" md-autofocus>
 *       </md-input-container>
 *     </form>
 *   </md-sidenav>
 * </div>
 * </hljs>
 *
 * <hljs lang="js">
 * var app = angular.module('myApp', ['ngMaterial']);
 * app.controller('MyController', function($scope, $mdSidenav) {
 *   $scope.openLeftMenu = function() {
 *     $mdSidenav('left').toggle();
 *   };
 * });
 * </hljs>
 *
 * @param {expression=} md-is-open A model bound to whether the sidenav is opened.
 * @param {boolean=} md-disable-backdrop When present in the markup, the sidenav will not show a
 *  backdrop.
 * @param {boolean=} md-disable-close-events When present in the markup, clicking the backdrop or
 *  pressing the 'Escape' key will not close the sidenav.
 * @param {string=} md-component-id componentId to use with $mdSidenav service.
 * @param {expression=} md-is-locked-open When this expression evaluates to true,
 * the sidenav 'locks open': it falls into the content's flow instead
 * of appearing over it. This overrides the `md-is-open` attribute.
 * @param {string=} md-disable-scroll-target Selector, pointing to an element, whose scrolling will
 * be disabled when the sidenav is opened. By default this is the sidenav's direct parent.
 *
* The $mdMedia() service is exposed to the is-locked-open attribute, which
 * can be given a media query or one of the `sm`, `gt-sm`, `md`, `gt-md`, `lg` or `gt-lg` presets.
 * Examples:
 *
 *   - `<md-sidenav md-is-locked-open="shouldLockOpen"></md-sidenav>`
 *   - `<md-sidenav md-is-locked-open="$mdMedia('min-width: 1000px')"></md-sidenav>`
 *   - `<md-sidenav md-is-locked-open="$mdMedia('sm')"></md-sidenav>` (locks open on small screens)
 */
function SidenavDirective($mdMedia, $mdUtil, $mdConstant, $mdTheming, $mdInteraction, $animate,
                          $compile, $parse, $log, $q, $document, $window, $$rAF) {
  return {
    restrict: 'E',
    scope: {
      isOpen: '=?mdIsOpen'
    },
    controller: '$mdSidenavController',
    compile: function(element) {
      element.addClass('md-closed').attr('tabIndex', '-1');
      return postLink;
    }
  };

  /**
   * Directive Post Link function...
   */
  function postLink(scope, element, attr, sidenavCtrl) {
    var lastParentOverFlow;
    var backdrop;
    var disableScrollTarget = null;
    var disableCloseEvents;
    var triggeringInteractionType;
    var triggeringElement = null;
    var previousContainerStyles;
    var promise = $q.when(true);
    var isLockedOpenParsed = $parse(attr.mdIsLockedOpen);
    var ngWindow = angular.element($window);
    var isLocked = function() {
      return isLockedOpenParsed(scope.$parent, {
        $media: function(arg) {
          $log.warn("$media is deprecated for is-locked-open. Use $mdMedia instead.");
          return $mdMedia(arg);
        },
        $mdMedia: $mdMedia
      });
    };

    if (attr.mdDisableScrollTarget) {
      disableScrollTarget = $document[0].querySelector(attr.mdDisableScrollTarget);

      if (disableScrollTarget) {
        disableScrollTarget = angular.element(disableScrollTarget);
      } else {
        $log.warn($mdUtil.supplant('mdSidenav: couldn\'t find element matching ' +
          'selector "{selector}". Falling back to parent.',
          { selector: attr.mdDisableScrollTarget }));
      }
    }

    if (!disableScrollTarget) {
      disableScrollTarget = element.parent();
    }

    // Only create the backdrop if the backdrop isn't disabled.
    if (!attr.hasOwnProperty('mdDisableBackdrop')) {
      backdrop = $mdUtil.createBackdrop(scope, "md-sidenav-backdrop md-opaque ng-enter");
    }

    // If md-disable-close-events is set on the sidenav we will disable
    // backdrop click and Escape key events
    if (attr.hasOwnProperty('mdDisableCloseEvents')) {
      disableCloseEvents = true;
    }

    element.addClass('_md');     // private md component indicator for styling
    $mdTheming(element);

    // The backdrop should inherit the sidenavs theme,
    // because the backdrop will take its parent theme by default.
    if (backdrop) $mdTheming.inherit(backdrop, element);

    element.on('$destroy', function() {
      backdrop && backdrop.remove();
      sidenavCtrl.destroy();
    });

    scope.$on('$destroy', function(){
      backdrop && backdrop.remove();
    });

    scope.$watch(isLocked, updateIsLocked);
    scope.$watch('isOpen', updateIsOpen);


    // Publish special accessor for the Controller instance
    sidenavCtrl.$toggleOpen = toggleOpen;

    /**
     * Toggle the DOM classes to indicate `locked`
     * @param isLocked
     * @param oldValue
     */
    function updateIsLocked(isLocked, oldValue) {
      scope.isLockedOpen = isLocked;
      if (isLocked === oldValue) {
        element.toggleClass('md-locked-open', !!isLocked);
      } else {
        $animate[isLocked ? 'addClass' : 'removeClass'](element, 'md-locked-open');
      }
      if (backdrop) {
        backdrop.toggleClass('md-locked-open', !!isLocked);
      }
    }

    /**
     * Toggle the SideNav view and attach/detach listeners
     * @param isOpen
     */
    function updateIsOpen(isOpen) {
      // Support deprecated md-sidenav-focus attribute as fallback
      var focusEl = $mdUtil.findFocusTarget(element) ||
        $mdUtil.findFocusTarget(element,'[md-sidenav-focus]') || element;
      var parent = element.parent();
      var restorePositioning;

      // If the user hasn't set the disable close events property we are adding
      // click and escape events to close the sidenav
      if (!disableCloseEvents) {
        parent[isOpen ? 'on' : 'off']('keydown', onKeyDown);
        if (backdrop) backdrop[isOpen ? 'on' : 'off']('click', close);
      }

      restorePositioning = updateContainerPositions(parent, isOpen);

      if (isOpen) {
        // Capture upon opening..
        triggeringElement = $document[0].activeElement;
        triggeringInteractionType = $mdInteraction.getLastInteractionType();
      }

      disableParentScroll(isOpen);

      return promise = $q.all([
        isOpen && backdrop ? $animate.enter(backdrop, parent) : backdrop ?
                             $animate.leave(backdrop) : $q.when(true),
        $animate[isOpen ? 'removeClass' : 'addClass'](element, 'md-closed')
      ]).then(function() {
        // Perform focus when animations are ALL done...
        if (scope.isOpen) {
          $$rAF(function() {
            // Notifies child components that the sidenav was opened. Should wait
            // a frame in order to allow for the element height to be computed.
            ngWindow.triggerHandler('resize');
          });

          focusEl && focusEl.focus();
        }

        // Restores the positioning on the sidenav and backdrop.
        restorePositioning && restorePositioning();
      });
    }

    function updateContainerPositions(parent, willOpen) {
      var drawerEl = element[0];
      var scrollTop = parent[0].scrollTop;

      if (willOpen && scrollTop) {
        previousContainerStyles = {
          top: drawerEl.style.top,
          bottom: drawerEl.style.bottom,
          height: drawerEl.style.height
        };

        // When the parent is scrolled down, then we want to be able to show the sidenav at the
        // current scroll position. We're moving the sidenav down to the correct scroll position
        // and apply the height of the parent, to increase the performance. Using 100% as height,
        // will impact the performance heavily.
        var positionStyle = {
          top: scrollTop + 'px',
          bottom: 'auto',
          height: parent[0].clientHeight + 'px'
        };

        // Apply the new position styles to the sidenav and backdrop.
        element.css(positionStyle);
        backdrop.css(positionStyle);
      }

      // When the sidenav is closing and we have previous defined container styles,
      // then we return a restore function, which resets the sidenav and backdrop.
      if (!willOpen && previousContainerStyles) {
        return function() {
          drawerEl.style.top = previousContainerStyles.top;
          drawerEl.style.bottom = previousContainerStyles.bottom;
          drawerEl.style.height = previousContainerStyles.height;

          backdrop[0].style.top = null;
          backdrop[0].style.bottom = null;
          backdrop[0].style.height = null;

          previousContainerStyles = null;
        };
      }
    }

    /**
     * Prevent parent scrolling (when the SideNav is open)
     */
    function disableParentScroll(disabled) {
      if (disabled && !lastParentOverFlow) {
        lastParentOverFlow = disableScrollTarget.css('overflow');
        disableScrollTarget.css('overflow', 'hidden');
      } else if (angular.isDefined(lastParentOverFlow)) {
        disableScrollTarget.css('overflow', lastParentOverFlow);
        lastParentOverFlow = undefined;
      }
    }

    /**
     * Toggle the sideNav view and publish a promise to be resolved when
     * the view animation finishes.
     * @param {boolean} isOpen true to open the sidenav, false to close it
     * @returns {*} promise to be resolved when the view animation finishes
     */
    function toggleOpen(isOpen) {
      if (scope.isOpen === isOpen) {
        return $q.when(true);
      } else {
        if (scope.isOpen && sidenavCtrl.onCloseCb) sidenavCtrl.onCloseCb();

        return $q(function(resolve) {
          // Toggle value to force an async `updateIsOpen()` to run
          scope.isOpen = isOpen;

          $mdUtil.nextTick(function() {
            // When the current `updateIsOpen()` animation finishes
            promise.then(function(result) {

              if (!scope.isOpen && triggeringElement && triggeringInteractionType === 'keyboard') {
                // reset focus to originating element (if available) upon close
                triggeringElement.focus();
                triggeringElement = null;
              }

              resolve(result);
            });
          });
        });
      }
    }

    /**
     * Auto-close sideNav when the `escape` key is pressed.
     * @param {KeyboardEvent} ev keydown event
     */
    function onKeyDown(ev) {
      var isEscape = (ev.keyCode === $mdConstant.KEY_CODE.ESCAPE);
      return isEscape ? close(ev) : $q.when(true);
    }

    /**
     * With backdrop `clicks` or `escape` key-press, immediately apply the CSS close transition...
     * Then notify the controller to close() and perform its own actions.
     * @param {Event} ev
     * @returns {*}
     */
    function close(ev) {
      ev.preventDefault();

      return sidenavCtrl.close();
    }
  }
}

/*
 * @private
 * @ngdoc controller
 * @name SidenavController
 * @module material.components.sidenav
 */
function SidenavController($scope, $attrs, $mdComponentRegistry, $q, $interpolate) {
  var self = this;

  // Use Default internal method until overridden by directive postLink

  // Synchronous getters
  self.isOpen = function() { return !!$scope.isOpen; };
  self.isLockedOpen = function() { return !!$scope.isLockedOpen; };

  // Synchronous setters
  self.onClose = function (callback) {
    self.onCloseCb = callback;
    return self;
  };

  // Async actions
  self.open   = function() { return self.$toggleOpen(true);  };
  self.close  = function() { return self.$toggleOpen(false); };
  self.toggle = function() { return self.$toggleOpen(!$scope.isOpen);  };
  self.$toggleOpen = function(value) { return $q.when($scope.isOpen = value); };

  // Evaluate the component id.
  var rawId = $attrs.mdComponentId;
  var hasDataBinding = rawId && rawId.indexOf($interpolate.startSymbol()) > -1;
  var componentId = hasDataBinding ? $interpolate(rawId)($scope.$parent) : rawId;

  // Register the component.
  self.destroy = $mdComponentRegistry.register(self, componentId);

  // Watch and update the component, if the id has changed.
  if (hasDataBinding) {
    $attrs.$observe('mdComponentId', function(id) {
      if (id && id !== self.$$mdHandle) {
        // `destroy` only deregisters the old component id so we can add the new one.
        self.destroy();
        self.destroy = $mdComponentRegistry.register(self, id);
      }
    });
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.slider
 */
SliderDirective.$inject = ["$$rAF", "$window", "$mdAria", "$mdUtil", "$mdConstant", "$mdTheming", "$mdGesture", "$parse", "$log", "$timeout"];
angular.module('material.components.slider', [
  'material.core'
])
.directive('mdSlider', SliderDirective)
.directive('mdSliderContainer', SliderContainerDirective);

/**
 * @type {number} the page size used for stepping when page up/down keys are pressed.
 */
var stepPageSize = 10;
/**
 * @type {number} the multiplier applied to a step when the arrow key is pressed along with
 *  alt, meta, or ctrl.
 */
var modifierMultiplier = 4;

/**
 * @ngdoc directive
 * @name mdSliderContainer
 * @module material.components.slider
 * @restrict E
 * @description
 * The `<md-slider-container>` can hold the slider with two other elements.
 * In this case, the other elements are a `span` for the label and an `input` for displaying
 * the model value.
 *
 * @usage
 * <hljs lang="html">
 *  <md-slider-container>
 *    <span>Red</span>
 *    <md-slider min="0" max="255" ng-model="color.red" aria-label="red" id="red-slider">
 *    </md-slider>
 *    <md-input-container>
 *      <input type="number" ng-model="color.red" aria-label="Red" aria-controls="red-slider">
 *    </md-input-container>
 *  </md-slider-container>
 * </hljs>
 */
function SliderContainerDirective() {
  return {
    controller: function () {},
    compile: function (elem) {
      var slider = elem.find('md-slider');

      if (!slider) {
        return;
      }

      var vertical = slider.attr('md-vertical');

      if (vertical !== undefined) {
        elem.attr('md-vertical', '');
      }

      if (!slider.attr('flex')) {
        slider.attr('flex', '');
      }

      return function postLink(scope, element, attr, ctrl) {
        element.addClass('_md');     // private md component indicator for styling

        // We have to manually stop the $watch on ngDisabled because it exists
        // on the parent scope, and won't be automatically destroyed when
        // the component is destroyed.
        function setDisable(value) {
          element.children().attr('disabled', value);
          element.find('input').attr('disabled', value);
        }

        var stopDisabledWatch = angular.noop;

        if (attr.disabled) {
          setDisable(true);
        }
        else if (attr.ngDisabled) {
          stopDisabledWatch = scope.$watch(attr.ngDisabled, function (value) {
            setDisable(value);
          });
        }

        scope.$on('$destroy', function () {
          stopDisabledWatch();
        });

        var initialMaxWidth;

        /**
         * @param {number} length of the input's string value
         */
        ctrl.fitInputWidthToTextLength = function (length) {
          var input = element[0].querySelector('md-input-container');

          if (input) {
            var computedStyle = getComputedStyle(input);
            var minWidth = parseInt(computedStyle.minWidth);
            var padding = parseInt(computedStyle.paddingLeft) + parseInt(computedStyle.paddingRight);

            initialMaxWidth = initialMaxWidth || parseInt(computedStyle.maxWidth);
            var newMaxWidth = Math.max(initialMaxWidth, minWidth + padding + (minWidth / 2 * length));

            input.style.maxWidth = newMaxWidth + 'px';
          }
        };
      };
    }
  };
}

/**
 * @ngdoc directive
 * @name mdSlider
 * @module material.components.slider
 * @restrict E
 * @description
 * The `<md-slider>` component allows the user to choose from a range of values.
 *
 * As per the [Material Design spec](https://material.io/archive/guidelines/style/color.html#color-color-system)
 * the slider is in the accent color by default. The primary color palette may be used with
 * the `md-primary` class.
 *
 * The slider has two modes:
 * - "normal" mode where the user slides between a wide range of values
 * - "discrete" mode where the user slides between only a few select values
 *
 * To enable discrete mode, add the `md-discrete` attribute to a slider
 * and use the `step` attribute to change the distance between
 * values the user is allowed to pick.
 *
 * When using the keyboard:
 * - pressing the arrow keys will increase or decrease the slider's value by one step
 * - holding the Meta, Control, or Alt key while pressing the arrow keys will
 *   move the slider four steps at a time
 * - pressing the Home key will move the slider to the first allowed value
 * - pressing the End key will move the slider to the last allowed value
 * - pressing the Page Up key will increase the slider value by ten
 * - pressing the Page Down key will decrease the slider value by ten
 *
 * @usage
 * <h4>Normal Mode</h4>
 * <hljs lang="html">
 * <md-slider ng-model="myValue" min="5" max="500">
 * </md-slider>
 * </hljs>
 * <h4>Discrete Mode</h4>
 * <hljs lang="html">
 * <md-slider md-discrete ng-model="myDiscreteValue" step="10" min="10" max="130">
 * </md-slider>
 * </hljs>
 * <h4>Invert Mode</h4>
 * <hljs lang="html">
 * <md-slider md-invert ng-model="myValue" step="10" min="10" max="130">
 * </md-slider>
 * </hljs>
 *
 * @param {expression} ng-model Assignable angular expression to be data-bound.
 *  The expression should evaluate to a `number`.
 * @param {expression=} ng-disabled If this expression evaluates as truthy, the slider will be
 *  disabled.
 * @param {expression=} ng-readonly If this expression evaluates as truthy, the slider will be in
 *  read only mode.
 * @param {boolean=} md-discrete If this attribute exists during initialization, enable discrete
 *  mode. Defaults to `false`.
 * @param {boolean=} md-vertical If this attribute exists during initialization, enable vertical
 *  orientation mode. Defaults to `false`.
 * @param {boolean=} md-invert If this attribute exists during initialization, enable inverted mode.
 *  Defaults to `false`.
 * @param {number=} step The distance between values the user is allowed to pick. Defaults to `1`.
 * @param {number=} min The minimum value the user is allowed to pick. Defaults to `0`.
 * @param {number=} max The maximum value the user is allowed to pick. Defaults to `100`.
 * @param {number=} round The amount of numbers after the decimal point. The maximum is 6 to
 *  prevent scientific notation. Defaults to `3`.
 */
function SliderDirective($$rAF, $window, $mdAria, $mdUtil, $mdConstant, $mdTheming, $mdGesture,
                         $parse, $log, $timeout) {
  return {
    scope: {},
    require: ['?ngModel', '?^mdSliderContainer'],
    template:
      '<div class="md-slider-wrapper">' +
        '<div class="md-slider-content">' +
          '<div class="md-track-container">' +
            '<div class="md-track"></div>' +
            '<div class="md-track md-track-fill"></div>' +
            '<div class="md-track-ticks"></div>' +
          '</div>' +
          '<div class="md-thumb-container">' +
            '<div class="md-thumb"></div>' +
            '<div class="md-focus-thumb"></div>' +
            '<div class="md-focus-ring"></div>' +
            '<div class="md-sign">' +
              '<span class="md-thumb-text"></span>' +
            '</div>' +
            '<div class="md-disabled-thumb"></div>' +
          '</div>' +
        '</div>' +
      '</div>',
    compile: compile
  };

  // **********************************************************
  // Private Methods
  // **********************************************************

  function compile (tElement, tAttrs) {
    var wrapper = angular.element(tElement[0].getElementsByClassName('md-slider-wrapper'));

    var tabIndex = tAttrs.tabindex || 0;
    wrapper.attr('tabindex', tabIndex);

    if (tAttrs.disabled || tAttrs.ngDisabled) wrapper.attr('tabindex', -1);

    wrapper.attr('role', 'slider');

    $mdAria.expect(tElement, 'aria-label');

    return postLink;
  }

  function postLink(scope, element, attr, ctrls) {
    $mdTheming(element);
    var ngModelCtrl = ctrls[0] || {
      // Mock ngModelController if it doesn't exist to give us
      // the minimum functionality needed
      $setViewValue: function(val) {
        this.$viewValue = val;
        this.$viewChangeListeners.forEach(function(cb) { cb(); });
      },
      $parsers: [],
      $formatters: [],
      $viewChangeListeners: []
    };

    var containerCtrl = ctrls[1];
    var container = angular.element($mdUtil.getClosest(element, '_md-slider-container', true));
    var isDisabled = attr.ngDisabled ? angular.bind(null, $parse(attr.ngDisabled), scope.$parent) : function () {
          return element[0].hasAttribute('disabled');
        };

    var thumb = angular.element(element[0].querySelector('.md-thumb'));
    var thumbText = angular.element(element[0].querySelector('.md-thumb-text'));
    var thumbContainer = thumb.parent();
    var trackContainer = angular.element(element[0].querySelector('.md-track-container'));
    var activeTrack = angular.element(element[0].querySelector('.md-track-fill'));
    var tickContainer = angular.element(element[0].querySelector('.md-track-ticks'));
    var wrapper = angular.element(element[0].getElementsByClassName('md-slider-wrapper'));
    var content = angular.element(element[0].getElementsByClassName('md-slider-content'));
    var throttledRefreshDimensions = $mdUtil.throttle(refreshSliderDimensions, 5000);

    // Default values, overridable by attrs
    var DEFAULT_ROUND = 3;
    var vertical = angular.isDefined(attr.mdVertical);
    var discrete = angular.isDefined(attr.mdDiscrete);
    var invert = angular.isDefined(attr.mdInvert);
    angular.isDefined(attr.min) ? attr.$observe('min', updateMin) : updateMin(0);
    angular.isDefined(attr.max) ? attr.$observe('max', updateMax) : updateMax(100);
    angular.isDefined(attr.step)? attr.$observe('step', updateStep) : updateStep(1);
    angular.isDefined(attr.round)? attr.$observe('round', updateRound) : updateRound(DEFAULT_ROUND);

    // We have to manually stop the $watch on ngDisabled because it exists
    // on the parent scope, and won't be automatically destroyed when
    // the component is destroyed.
    var stopDisabledWatch = angular.noop;
    if (attr.ngDisabled) {
      stopDisabledWatch = scope.$parent.$watch(attr.ngDisabled, updateAriaDisabled);
    }

    $mdGesture.register(wrapper, 'drag', { horizontal: !vertical });

    scope.mouseActive = false;

    wrapper
      .on('keydown', keydownListener)
      .on('mousedown', mouseDownListener)
      .on('focus', focusListener)
      .on('blur', blurListener)
      .on('$md.pressdown', onPressDown)
      .on('$md.pressup', onPressUp)
      .on('$md.dragstart', onDragStart)
      .on('$md.drag', onDrag)
      .on('$md.dragend', onDragEnd);

    // On resize, recalculate the slider's dimensions and re-render
    function updateAll() {
      refreshSliderDimensions();
      ngModelRender();
    }
    setTimeout(updateAll, 0);

    var debouncedUpdateAll = $$rAF.throttle(updateAll);
    angular.element($window).on('resize', debouncedUpdateAll);

    scope.$on('$destroy', function() {
      angular.element($window).off('resize', debouncedUpdateAll);
    });

    ngModelCtrl.$render = ngModelRender;
    ngModelCtrl.$viewChangeListeners.push(ngModelRender);
    ngModelCtrl.$formatters.push(minMaxValidator);
    ngModelCtrl.$formatters.push(stepValidator);

    /**
     * Attributes
     */
    var min;
    var max;
    var step;
    var round;
    function updateMin(value) {
      min = parseFloat(value);
      ngModelCtrl.$viewValue = minMaxValidator(ngModelCtrl.$modelValue, min, max);
      wrapper.attr('aria-valuemin', value);
      updateAll();
    }
    function updateMax(value) {
      max = parseFloat(value);
      ngModelCtrl.$viewValue = minMaxValidator(ngModelCtrl.$modelValue, min, max);
      wrapper.attr('aria-valuemax', value);
      updateAll();
    }
    function updateStep(value) {
      step = parseFloat(value);
    }
    function updateRound(value) {
      // Set max round digits to 6, after 6 the input uses scientific notation
      round = minMaxValidator(parseInt(value), 0, 6);
    }
    function updateAriaDisabled() {
      element.attr('aria-disabled', !!isDisabled());
    }

    // Draw the ticks with canvas.
    // The alternative to drawing ticks with canvas is to draw one element for each tick,
    // which could quickly become a performance bottleneck.
    var tickCanvas, tickCtx;
    function redrawTicks() {
      if (!discrete || isDisabled()) return;
      if (angular.isUndefined(step))         return;

      if (step <= 0) {
        var msg = 'Slider step value must be greater than zero when in discrete mode';
        $log.error(msg);
        throw new Error(msg);
      }

      var numSteps = Math.floor((max - min) / step);
      if (!tickCanvas) {
        tickCanvas = angular.element('<canvas>').css('position', 'absolute');
        tickContainer.append(tickCanvas);

        tickCtx = tickCanvas[0].getContext('2d');
      }

      var dimensions = getSliderDimensions();

      // If `dimensions` doesn't have height and width it might be the first attempt so we will refresh dimensions
      if (dimensions && !dimensions.height && !dimensions.width) {
        refreshSliderDimensions();
        dimensions = sliderDimensions;
      }

      tickCanvas[0].width = dimensions.width;
      tickCanvas[0].height = dimensions.height;

      var distance;
      for (var i = 0; i <= numSteps; i++) {
        var trackTicksStyle = $window.getComputedStyle(tickContainer[0]);
        tickCtx.fillStyle = trackTicksStyle.color || 'black';

        distance = Math.floor((vertical ? dimensions.height : dimensions.width) * (i / numSteps));

        tickCtx.fillRect(vertical ? 0 : distance - 1,
          vertical ? distance - 1 : 0,
          vertical ? dimensions.width : 2,
          vertical ? 2 : dimensions.height);
      }
    }

    function clearTicks() {
      if (tickCanvas && tickCtx) {
        var dimensions = getSliderDimensions();
        tickCtx.clearRect(0, 0, dimensions.width, dimensions.height);
      }
    }

    /**
     * Refreshing Dimensions
     */
    var sliderDimensions = {};
    refreshSliderDimensions();
    function refreshSliderDimensions() {
      sliderDimensions = trackContainer[0].getBoundingClientRect();
    }
    function getSliderDimensions() {
      throttledRefreshDimensions();
      return sliderDimensions;
    }

    /**
     * left/right/up/down arrow listener
     * @param {!KeyboardEvent} ev
     */
    function keydownListener(ev) {
      if (isDisabled()) return;
      var keyCodes = $mdConstant.KEY_CODE;

      var changeAmount;
      switch (ev.keyCode) {
        case keyCodes.DOWN_ARROW:
        case keyCodes.LEFT_ARROW:
          ev.preventDefault();
          changeAmount = -step;
          break;
        case keyCodes.UP_ARROW:
        case keyCodes.RIGHT_ARROW:
          ev.preventDefault();
          changeAmount = step;
          break;
        case keyCodes.PAGE_DOWN:
          ev.preventDefault();
          changeAmount = -step * stepPageSize;
          break;
        case keyCodes.PAGE_UP:
          ev.preventDefault();
          changeAmount = step * stepPageSize;
          break;
        case keyCodes.HOME:
          ev.preventDefault();
          ev.stopPropagation();
          updateValue(min);
          break;
        case keyCodes.END:
          ev.preventDefault();
          ev.stopPropagation();
          updateValue(max);
          break;
      }
      if (changeAmount) {
        changeAmount = invert ? -changeAmount : changeAmount;
        if (ev.metaKey || ev.ctrlKey || ev.altKey) {
          changeAmount *= modifierMultiplier;
        }
        ev.preventDefault();
        ev.stopPropagation();
        updateValue(ngModelCtrl.$viewValue + changeAmount);
      }
    }

    /**
     * @param value new slider value used for setting the model value
     */
    function updateValue(value) {
      scope.$evalAsync(function() {
        setModelValue(value);
      });
    }

    function mouseDownListener() {
      redrawTicks();

      scope.mouseActive = true;
      wrapper.removeClass('md-focused');

      $timeout(function() {
        scope.mouseActive = false;
      }, 100);
    }

    function focusListener() {
      if (scope.mouseActive === false) {
        wrapper.addClass('md-focused');
      }
    }

    function blurListener() {
      wrapper.removeClass('md-focused');
      element.removeClass('md-active');
      clearTicks();
    }

    /**
     * ngModel setters and validators
     */
    function setModelValue(value) {
      ngModelCtrl.$setViewValue(minMaxValidator(stepValidator(value)));
    }
    function ngModelRender() {
      if (isNaN(ngModelCtrl.$viewValue)) {
        ngModelCtrl.$viewValue = ngModelCtrl.$modelValue;
      }

      ngModelCtrl.$viewValue = minMaxValidator(ngModelCtrl.$viewValue);

      var percent = valueToPercent(ngModelCtrl.$viewValue);
      scope.modelValue = ngModelCtrl.$viewValue;
      wrapper.attr('aria-valuenow', ngModelCtrl.$viewValue);
      setSliderPercent(percent);
      thumbText.text(ngModelCtrl.$viewValue);
    }

    function minMaxValidator(value, minValue, maxValue) {
      if (angular.isNumber(value)) {
        minValue = angular.isNumber(minValue) ? minValue : min;
        maxValue = angular.isNumber(maxValue) ? maxValue : max;

        return Math.max(minValue, Math.min(maxValue, value));
      }
    }

    function stepValidator(value) {
      if (angular.isNumber(value)) {
        var formattedValue = (Math.round((value - min) / step) * step + min);
        formattedValue = (Math.round(formattedValue * Math.pow(10, round)) / Math.pow(10, round));

        if (containerCtrl && containerCtrl.fitInputWidthToTextLength) {
          $mdUtil.debounce(function () {
            containerCtrl.fitInputWidthToTextLength(formattedValue.toString().length);
          }, 100)();
        }

        return formattedValue;
      }
    }

    /**
     * @param {number} percent 0-1
     */
    function setSliderPercent(percent) {

      percent = clamp(percent);

      var thumbPosition = (percent * 100) + '%';
      var activeTrackPercent = invert ? (1 - percent) * 100 + '%' : thumbPosition;

      if (vertical) {
        thumbContainer.css('bottom', thumbPosition);
      }
      else {
        $mdUtil.bidiProperty(thumbContainer, 'left', 'right', thumbPosition);
      }


      activeTrack.css(vertical ? 'height' : 'width', activeTrackPercent);

      element.toggleClass((invert ? 'md-max' : 'md-min'), percent === 0);
      element.toggleClass((invert ? 'md-min' : 'md-max'), percent === 1);
    }

    /**
     * Slide listeners
     */
    var isDragging = false;

    function onPressDown(ev) {
      if (isDisabled()) return;

      element.addClass('md-active');
      element[0].focus();
      refreshSliderDimensions();

      var exactVal = percentToValue(positionToPercent(vertical ? ev.srcEvent.clientY : ev.srcEvent.clientX));
      var closestVal = minMaxValidator(stepValidator(exactVal));
      scope.$apply(function() {
        setModelValue(closestVal);
        setSliderPercent(valueToPercent(closestVal));
      });
    }
    function onPressUp(ev) {
      if (isDisabled()) return;

      element.removeClass('md-dragging');

      var exactVal = percentToValue(positionToPercent(vertical ? ev.srcEvent.clientY : ev.srcEvent.clientX));
      var closestVal = minMaxValidator(stepValidator(exactVal));
      scope.$apply(function() {
        setModelValue(closestVal);
        ngModelRender();
      });
    }
    function onDragStart(ev) {
      if (isDisabled()) return;
      isDragging = true;

      ev.stopPropagation();

      element.addClass('md-dragging');
      setSliderFromEvent(ev);
    }
    function onDrag(ev) {
      if (!isDragging) return;
      ev.stopPropagation();
      setSliderFromEvent(ev);
    }
    function onDragEnd(ev) {
      if (!isDragging) return;
      ev.stopPropagation();
      isDragging = false;
    }

    function setSliderFromEvent(ev) {
      // While panning discrete, update only the
      // visual positioning but not the model value.
      if (discrete) adjustThumbPosition(vertical ? ev.srcEvent.clientY : ev.srcEvent.clientX);
      else            doSlide(vertical ? ev.srcEvent.clientY : ev.srcEvent.clientX);
    }

    /**
     * Slide the UI by changing the model value
     * @param x
     */
    function doSlide(x) {
      scope.$evalAsync(function() {
        setModelValue(percentToValue(positionToPercent(x)));
      });
    }

    /**
     * Slide the UI without changing the model (while dragging/panning)
     * @param x
     */
    function adjustThumbPosition(x) {
      var exactVal = percentToValue(positionToPercent(x));
      var closestVal = minMaxValidator(stepValidator(exactVal));
      setSliderPercent(positionToPercent(x));
      thumbText.text(closestVal);
    }

    /**
    * Clamps the value to be between 0 and 1.
    * @param {number} value The value to clamp.
    * @returns {number}
    */
    function clamp(value) {
      return Math.max(0, Math.min(value || 0, 1));
    }

    /**
     * Convert position on slider to percentage value of offset from beginning...
     * @param position
     * @returns {number}
     */
    function positionToPercent(position) {
      var offset = vertical ? sliderDimensions.top : sliderDimensions.left;
      var size = vertical ? sliderDimensions.height : sliderDimensions.width;
      var calc = (position - offset) / size;

      if (!vertical && $mdUtil.isRtl(attr)) {
        calc = 1 - calc;
      }

      return Math.max(0, Math.min(1, vertical ? 1 - calc : calc));
    }

    /**
     * Convert percentage offset on slide to equivalent model value
     * @param percent
     * @returns {*}
     */
    function percentToValue(percent) {
      var adjustedPercent = invert ? (1 - percent) : percent;
      return (min + adjustedPercent * (max - min));
    }

    function valueToPercent(val) {
      var percent = (val - min) / (max - min);
      return invert ? (1 - percent) : percent;
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.sticky
 * @description
 * Sticky effects for md
 *
 */
MdSticky.$inject = ["$mdConstant", "$$rAF", "$mdUtil", "$compile"];
angular
  .module('material.components.sticky', [
    'material.core',
    'material.components.content'
  ])
  .factory('$mdSticky', MdSticky);

/**
 * @ngdoc service
 * @name $mdSticky
 * @module material.components.sticky
 *
 * @description
 * The `$mdSticky`service provides a mixin to make elements sticky.
 *
 * Whenever the current browser supports stickiness natively, the `$mdSticky` service will just
 * use the native browser stickiness.
 *
 * By default the `$mdSticky` service compiles the cloned element, when not specified through the `elementClone`
 * parameter, in the same scope as the actual element lives.
 *
 *
 * <h3>Notes</h3>
 * When using an element which is containing a compiled directive, which changed its DOM structure during compilation,
 * you should compile the clone yourself using the plain template.<br/><br/>
 * See the right usage below:
 * <hljs lang="js">
 *   angular.module('myModule')
 *     .directive('stickySelect', function($mdSticky, $compile) {
 *       var SELECT_TEMPLATE =
 *         '<md-select ng-model="selected">' +
 *           '<md-option>Option 1</md-option>' +
 *         '</md-select>';
 *
 *       return {
 *         restrict: 'E',
 *         replace: true,
 *         template: SELECT_TEMPLATE,
 *         link: function(scope,element) {
 *           $mdSticky(scope, element, $compile(SELECT_TEMPLATE)(scope));
 *         }
 *       };
 *     });
 * </hljs>
 *
 * @usage
 * <hljs lang="js">
 *   angular.module('myModule')
 *     .directive('stickyText', function($mdSticky, $compile) {
 *       return {
 *         restrict: 'E',
 *         template: '<span>Sticky Text</span>',
 *         link: function(scope,element) {
 *           $mdSticky(scope, element);
 *         }
 *       };
 *     });
 * </hljs>
 *
 * @returns A `$mdSticky` function that takes three arguments:
 *   - `scope`
 *   - `element`: The element that will be 'sticky'
 *   - `elementClone`: A clone of the element, that will be shown
 *     when the user starts scrolling past the original element.
 *     If not provided, it will use the result of `element.clone()` and compiles it in the given scope.
 */
function MdSticky($mdConstant, $$rAF, $mdUtil, $compile) {

  var browserStickySupport = $mdUtil.checkStickySupport();

  /**
   * Registers an element as sticky, used internally by directives to register themselves
   */
  return function registerStickyElement(scope, element, stickyClone) {
    var contentCtrl = element.controller('mdContent');
    if (!contentCtrl) return;

    if (browserStickySupport) {
      element.css({
        position: browserStickySupport,
        top: 0,
        'z-index': 2
      });
    } else {
      var $$sticky = contentCtrl.$element.data('$$sticky');
      if (!$$sticky) {
        $$sticky = setupSticky(contentCtrl);
        contentCtrl.$element.data('$$sticky', $$sticky);
      }

      // Compile our cloned element, when cloned in this service, into the given scope.
      var cloneElement = stickyClone || $compile(element.clone())(scope);

      var deregister = $$sticky.add(element, cloneElement);
      scope.$on('$destroy', deregister);
    }
  };

  function setupSticky(contentCtrl) {
    var contentEl = contentCtrl.$element;

    // Refresh elements is very expensive, so we use the debounced
    // version when possible.
    var debouncedRefreshElements = $$rAF.throttle(refreshElements);

    // setupAugmentedScrollEvents gives us `$scrollstart` and `$scroll`,
    // more reliable than `scroll` on android.
    setupAugmentedScrollEvents(contentEl);
    contentEl.on('$scrollstart', debouncedRefreshElements);
    contentEl.on('$scroll', onScroll);

    var self;
    return self = {
      prev: null,
      current: null, // the currently stickied item
      next: null,
      items: [],
      add: add,
      refreshElements: refreshElements
    };

    /***************
     * Public
     ***************/
    // Add an element and its sticky clone to this content's sticky collection
    function add(element, stickyClone) {
      stickyClone.addClass('md-sticky-clone');

      var item = {
        element: element,
        clone: stickyClone
      };
      self.items.push(item);

      $mdUtil.nextTick(function() {
        contentEl.prepend(item.clone);
      });

      debouncedRefreshElements();

      return function remove() {
        self.items.forEach(function(item, index) {
          if (item.element[0] === element[0]) {
            self.items.splice(index, 1);
            item.clone.remove();
          }
        });
        debouncedRefreshElements();
      };
    }

    function refreshElements() {
      // Sort our collection of elements by their current position in the DOM.
      // We need to do this because our elements' order of being added may not
      // be the same as their order of display.
      self.items.forEach(refreshPosition);
      self.items = self.items.sort(function(a, b) {
        return a.top < b.top ? -1 : 1;
      });

      // Find which item in the list should be active,
      // based upon the content's current scroll position
      var item;
      var currentScrollTop = contentEl.prop('scrollTop');
      for (var i = self.items.length - 1; i >= 0; i--) {
        if (currentScrollTop > self.items[i].top) {
          item = self.items[i];
          break;
        }
      }
      setCurrentItem(item);
    }

    /***************
     * Private
     ***************/

    // Find the `top` of an item relative to the content element,
    // and also the height.
    function refreshPosition(item) {
      // Find the top of an item by adding to the offsetHeight until we reach the
      // content element.
      var current = item.element[0];
      item.top = 0;
      item.left = 0;
      item.right = 0;
      while (current && current !== contentEl[0]) {
        item.top += current.offsetTop;
        item.left += current.offsetLeft;
        if (current.offsetParent) {
          // Compute offsetRight
          item.right += current.offsetParent.offsetWidth - current.offsetWidth - current.offsetLeft;
        }
        current = current.offsetParent;
      }
      item.height = item.element.prop('offsetHeight');

      var defaultVal = $mdUtil.floatingScrollbars() ? '0' : undefined;
      $mdUtil.bidi(item.clone, 'margin-left', item.left, defaultVal);
      $mdUtil.bidi(item.clone, 'margin-right', defaultVal, item.right);
    }

    // As we scroll, push in and select the correct sticky element.
    function onScroll() {
      var scrollTop = contentEl.prop('scrollTop');
      var isScrollingDown = scrollTop > (onScroll.prevScrollTop || 0);

      // Store the previous scroll so we know which direction we are scrolling
      onScroll.prevScrollTop = scrollTop;

      //
      // AT TOP (not scrolling)
      //
      if (scrollTop === 0) {
        // If we're at the top, just clear the current item and return
        setCurrentItem(null);
        return;
      }

      //
      // SCROLLING DOWN (going towards the next item)
      //
      if (isScrollingDown) {

        // If we've scrolled down past the next item's position, sticky it and return
        if (self.next && self.next.top <= scrollTop) {
          setCurrentItem(self.next);
          return;
        }

        // If the next item is close to the current one, push the current one up out of the way
        if (self.current && self.next && self.next.top - scrollTop <= self.next.height) {
          translate(self.current, scrollTop + (self.next.top - self.next.height - scrollTop));
          return;
        }
      }

      //
      // SCROLLING UP (not at the top & not scrolling down; must be scrolling up)
      //
      if (!isScrollingDown) {

        // If we've scrolled up past the previous item's position, sticky it and return
        if (self.current && self.prev && scrollTop < self.current.top) {
          setCurrentItem(self.prev);
          return;
        }

        // If the next item is close to the current one, pull the current one down into view
        if (self.next && self.current && (scrollTop >= (self.next.top - self.current.height))) {
          translate(self.current, scrollTop + (self.next.top - scrollTop - self.current.height));
          return;
        }
      }

      //
      // Otherwise, just move the current item to the proper place (scrolling up or down)
      //
      if (self.current) {
        translate(self.current, scrollTop);
      }
    }

    function setCurrentItem(item) {
      if (self.current === item) return;
      // Deactivate currently active item
      if (self.current) {
        translate(self.current, null);
        setStickyState(self.current, null);
      }

      // Activate new item if given
      if (item) {
        setStickyState(item, 'active');
      }

      self.current = item;
      var index = self.items.indexOf(item);
      // If index === -1, index + 1 = 0. It works out.
      self.next = self.items[index + 1];
      self.prev = self.items[index - 1];
      setStickyState(self.next, 'next');
      setStickyState(self.prev, 'prev');
    }

    function setStickyState(item, state) {
      if (!item || item.state === state) return;
      if (item.state) {
        item.clone.attr('sticky-prev-state', item.state);
        item.element.attr('sticky-prev-state', item.state);
      }
      item.clone.attr('sticky-state', state);
      item.element.attr('sticky-state', state);
      item.state = state;
    }

    function translate(item, amount) {
      if (!item) return;
      if (amount === null || amount === undefined) {
        if (item.translateY) {
          item.translateY = null;
          item.clone.css($mdConstant.CSS.TRANSFORM, '');
        }
      } else {
        item.translateY = amount;

        $mdUtil.bidi(item.clone, $mdConstant.CSS.TRANSFORM,
          'translate3d(' + item.left + 'px,' + amount + 'px,0)',
          'translateY(' + amount + 'px)'
        );
      }
    }
  }


  // Android 4.4 don't accurately give scroll events.
  // To fix this problem, we setup a fake scroll event. We say:
  // > If a scroll or touchmove event has happened in the last DELAY milliseconds,
  //   then send a `$scroll` event every animationFrame.
  // Additionally, we add $scrollstart and $scrollend events.
  function setupAugmentedScrollEvents(element) {
    var SCROLL_END_DELAY = 200;
    var isScrolling;
    var lastScrollTime;
    element.on('scroll touchmove', function() {
      if (!isScrolling) {
        isScrolling = true;
        $$rAF.throttle(loopScrollEvent);
        element.triggerHandler('$scrollstart');
      }
      element.triggerHandler('$scroll');
      lastScrollTime = +$mdUtil.now();
    });

    function loopScrollEvent() {
      if (+$mdUtil.now() - lastScrollTime > SCROLL_END_DELAY) {
        isScrolling = false;
        element.triggerHandler('$scrollend');
      } else {
        element.triggerHandler('$scroll');
        $$rAF.throttle(loopScrollEvent);
      }
    }
  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.subheader
 * @description
 * SubHeader module
 *
 *  Subheaders are special list tiles that delineate distinct sections of a
 *  list or grid list and are typically related to the current filtering or
 *  sorting criteria. Subheader tiles are either displayed inline with tiles or
 *  can be associated with content, for example, in an adjacent column.
 *
 *  Upon scrolling, subheaders remain pinned to the top of the screen and remain
 *  pinned until pushed on or off screen by the next subheader. @see [Material
 *  Design Specifications](https://material.io/archive/guidelines/components/subheaders.html)
 *
 *  > To improve the visual grouping of content, use the system color for your subheaders.
 *
 */
MdSubheaderDirective.$inject = ["$mdSticky", "$compile", "$mdTheming", "$mdUtil", "$mdAria"];
angular
  .module('material.components.subheader', [
    'material.core',
    'material.components.sticky'
  ])
  .directive('mdSubheader', MdSubheaderDirective);

/**
 * @ngdoc directive
 * @name mdSubheader
 * @module material.components.subheader
 *
 * @restrict E
 *
 * @description
 * The `md-subheader` directive creates a sticky subheader for a section.
 *
 * Developers are able to disable the stickiness of the subheader by using the following markup
 *
 * <hljs lang="html">
 *   <md-subheader class="md-no-sticky">Not Sticky</md-subheader>
 * </hljs>
 *
 * ### Notes
 * - The `md-subheader` directive uses the <a ng-href="api/service/$mdSticky">$mdSticky</a> service
 * to make the subheader sticky.
 *
 * > Whenever the current browser doesn't support stickiness natively, the subheader
 * will be compiled twice to create a sticky clone of the subheader.
 *
 * @usage
 * <hljs lang="html">
 * <md-subheader>Online Friends</md-subheader>
 * </hljs>
 */

function MdSubheaderDirective($mdSticky, $compile, $mdTheming, $mdUtil, $mdAria) {
  return {
    restrict: 'E',
    replace: true,
    transclude: true,
    template: (
    '<div class="md-subheader _md">' +
    '  <div class="md-subheader-inner">' +
    '    <div class="md-subheader-content"></div>' +
    '  </div>' +
    '</div>'
    ),
    link: function postLink(scope, element, attr, controllers, transclude) {
      $mdTheming(element);
      element.addClass('_md');

      // Remove the ngRepeat attribute from the root element, because we don't want to compile
      // the ngRepeat for the sticky clone again.
      $mdUtil.prefixer().removeAttribute(element, 'ng-repeat');

      var outerHTML = element[0].outerHTML;

      function getContent(el) {
        return angular.element(el[0].querySelector('.md-subheader-content'));
      }

      // Set the ARIA attributes on the original element since it keeps it's original place in
      // the DOM, whereas the clones are in reverse order. Should be done after the outerHTML,
      // in order to avoid having multiple element be marked as headers.
      attr.$set('role', 'heading');
      $mdAria.expect(element, 'aria-level', '2');

      // Transclude the user-given contents of the subheader
      // the conventional way.
      transclude(scope, function(clone) {
        getContent(element).append(clone);
      });

      // Create another clone, that uses the outer and inner contents
      // of the element, that will be 'stickied' as the user scrolls.
      if (!element.hasClass('md-no-sticky')) {
        transclude(scope, function(clone) {
          // If the user adds an ng-if or ng-repeat directly to the md-subheader element, the
          // compiled clone below will only be a comment tag (since they replace their elements with
          // a comment) which cannot be properly passed to the $mdSticky; so we wrap it in our own
          // DIV to ensure we have something $mdSticky can use
          var wrapper = $compile('<div class="md-subheader-wrapper" aria-hidden="true">' + outerHTML + '</div>')(scope);

          // Delay initialization until after any `ng-if`/`ng-repeat`/etc has finished before
          // attempting to create the clone
          $mdUtil.nextTick(function() {
            // Append our transcluded clone into the wrapper.
            // We don't have to recompile the element again, because the clone is already
            // compiled in it's transclusion scope. If we recompile the outerHTML of the new clone, we would lose
            // our ngIf's and other previous registered bindings / properties.
            getContent(wrapper).append(clone);
          });

          // Make the element sticky and provide the stickyClone our self, to avoid recompilation of the subheader
          // element.
          $mdSticky(scope, element, wrapper);
        });
      }
    }
  };
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.swipe
 * @description Swipe module!
 */
/**
 * @ngdoc directive
 * @module material.components.swipe
 * @name mdSwipeLeft
 *
 * @restrict A
 *
 * @description
 * The md-swipe-left directive allows you to specify custom behavior when an element is swiped
 * left.
 *
 * ### Notes
 * - The `$event.currentTarget` of the swiped element will be `null`, but you can get a
 * reference to the element that actually holds the `md-swipe-left` directive by using
 * `$target.current`
 *
 * > You can see this in action on the <a ng-href="demo/swipe">demo page</a> (Look at the Developer
 * Tools console while swiping).
 *
 * @usage
 * <hljs lang="html">
 * <div md-swipe-left="onSwipeLeft($event, $target)">Swipe me left!</div>
 * </hljs>
 */
/**
 * @ngdoc directive
 * @module material.components.swipe
 * @name mdSwipeRight
 *
 * @restrict A
 *
 * @description
 * The md-swipe-right directive allows you to specify custom behavior when an element is swiped
 * right.
 *
 * ### Notes
 * - The `$event.currentTarget` of the swiped element will be `null`, but you can get a
 * reference to the element that actually holds the `md-swipe-right` directive by using
 * `$target.current`
 *
 * > You can see this in action on the <a ng-href="demo/swipe">demo page</a> (Look at the Developer
 * Tools console while swiping).
 *
 * @usage
 * <hljs lang="html">
 * <div md-swipe-right="onSwipeRight($event, $target)">Swipe me right!</div>
 * </hljs>
 */
/**
 * @ngdoc directive
 * @module material.components.swipe
 * @name mdSwipeUp
 *
 * @restrict A
 *
 * @description
 * The md-swipe-up directive allows you to specify custom behavior when an element is swiped
 * up.
 *
 * ### Notes
 * - The `$event.currentTarget` of the swiped element will be `null`, but you can get a
 * reference to the element that actually holds the `md-swipe-up` directive by using
 * `$target.current`
 *
 * > You can see this in action on the <a ng-href="demo/swipe">demo page</a> (Look at the Developer
 * Tools console while swiping).
 *
 * @usage
 * <hljs lang="html">
 * <div md-swipe-up="onSwipeUp($event, $target)">Swipe me up!</div>
 * </hljs>
 */
/**
 * @ngdoc directive
 * @module material.components.swipe
 * @name mdSwipeDown
 *
 * @restrict A
 *
 * @description
 * The md-swipe-down directive allows you to specify custom behavior when an element is swiped
 * down.
 *
 * ### Notes
 * - The `$event.currentTarget` of the swiped element will be `null`, but you can get a
 * reference to the element that actually holds the `md-swipe-down` directive by using
 * `$target.current`
 *
 * > You can see this in action on the <a ng-href="demo/swipe">demo page</a> (Look at the Developer
 * Tools console while swiping).
 *
 * @usage
 * <hljs lang="html">
 * <div md-swipe-down="onSwipeDown($event, $target)">Swipe me down!</div>
 * </hljs>
 */

angular.module('material.components.swipe', ['material.core'])
    .directive('mdSwipeLeft', getDirective('SwipeLeft'))
    .directive('mdSwipeRight', getDirective('SwipeRight'))
    .directive('mdSwipeUp', getDirective('SwipeUp'))
    .directive('mdSwipeDown', getDirective('SwipeDown'));

function getDirective(name) {
    DirectiveFactory.$inject = ["$parse"];
  var directiveName = 'md' + name;
  var eventName = '$md.' + name.toLowerCase();

  return DirectiveFactory;

  /* @ngInject */
  function DirectiveFactory($parse) {
      return { restrict: 'A', link: postLink };
      function postLink(scope, element, attr) {
        var fn = $parse(attr[directiveName]);
        element.on(eventName, function(ev) {
          var currentTarget = ev.currentTarget;
          scope.$applyAsync(function() { fn(scope, { $event: ev, $target: { current: currentTarget } }); });
        });
      }
    }
}



})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.switch
 */

MdSwitch.$inject = ["mdCheckboxDirective", "$mdUtil", "$mdConstant", "$parse", "$$rAF", "$mdGesture", "$timeout"];
angular.module('material.components.switch', [
  'material.core',
  'material.components.checkbox'
])
  .directive('mdSwitch', MdSwitch);

/**
 * @ngdoc directive
 * @module material.components.switch
 * @name mdSwitch
 * @restrict E
 *
 * The switch directive is used very much like the normal [angular checkbox](https://docs.angularjs.org/api/ng/input/input%5Bcheckbox%5D).
 *
 * As per the [Material Design spec](https://material.io/archive/guidelines/style/color.html#color-color-system)
 * the switch is in the accent color by default. The primary color palette may be used with
 * the `md-primary` class.
 *
 * @param {expression} ng-model Assignable angular expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {expression=} ng-true-value The value to which the expression should be set when selected.
 * @param {expression=} ng-false-value The value to which the expression should be set when not selected.
 * @param {expression=} ng-change Expression to be executed when the model value changes.
 * @param {expression=} ng-disabled En/Disable based on the expression.
 * @param {boolean=} md-no-ink Use of attribute indicates use of ripple ink effects.
 * @param {string=} aria-label Publish the button label used by screen-readers for accessibility. Defaults to the switch's text.
 * @param {boolean=} md-invert When set to true, the switch will be inverted.
 *
 * @usage
 * <hljs lang="html">
 * <md-switch ng-model="isActive" aria-label="Finished?">
 *   Finished ?
 * </md-switch>
 *
 * <md-switch md-no-ink ng-model="hasInk" aria-label="No Ink Effects">
 *   No Ink Effects
 * </md-switch>
 *
 * <md-switch ng-disabled="true" ng-model="isDisabled" aria-label="Disabled">
 *   Disabled
 * </md-switch>
 *
 * </hljs>
 */
function MdSwitch(mdCheckboxDirective, $mdUtil, $mdConstant, $parse, $$rAF, $mdGesture, $timeout) {
  var checkboxDirective = mdCheckboxDirective[0];

  return {
    restrict: 'E',
    priority: $mdConstant.BEFORE_NG_ARIA,
    transclude: true,
    template:
      '<div class="md-container">' +
        '<div class="md-bar"></div>' +
        '<div class="md-thumb-container">' +
          '<div class="md-thumb" md-ink-ripple md-ink-ripple-checkbox></div>' +
        '</div>'+
      '</div>' +
      '<div ng-transclude class="md-label"></div>',
    require: ['^?mdInputContainer', '?ngModel', '?^form'],
    compile: mdSwitchCompile
  };

  function mdSwitchCompile(element, attr) {
    var checkboxLink = checkboxDirective.compile(element, attr).post;
    // No transition on initial load.
    element.addClass('md-dragging');

    return function (scope, element, attr, ctrls) {
      var containerCtrl = ctrls[0];
      var ngModel = ctrls[1] || $mdUtil.fakeNgModel();
      var formCtrl = ctrls[2];

      var disabledGetter = null;
      if (attr.disabled != null) {
        disabledGetter = function() { return true; };
      } else if (attr.ngDisabled) {
        disabledGetter = $parse(attr.ngDisabled);
      }

      var thumbContainer = angular.element(element[0].querySelector('.md-thumb-container'));
      var switchContainer = angular.element(element[0].querySelector('.md-container'));
      var labelContainer = angular.element(element[0].querySelector('.md-label'));

      // no transition on initial load
      $$rAF(function() {
        element.removeClass('md-dragging');
      });

      checkboxLink(scope, element, attr, ctrls);

      if (disabledGetter) {
        scope.$watch(disabledGetter, function(isDisabled) {
          element.attr('tabindex', isDisabled ? -1 : 0);
        });
      }

      attr.$observe('mdInvert', function(newValue) {
        var isInverted = $mdUtil.parseAttributeBoolean(newValue);

        isInverted ? element.prepend(labelContainer) : element.prepend(switchContainer);

        // Toggle a CSS class to update the margin.
        element.toggleClass('md-inverted', isInverted);
      });

      // These events are triggered by setup drag
      $mdGesture.register(switchContainer, 'drag');
      switchContainer
        .on('$md.dragstart', onDragStart)
        .on('$md.drag', onDrag)
        .on('$md.dragend', onDragEnd);

      var drag;
      function onDragStart(ev) {
        // Don't go if the switch is disabled.
        if (disabledGetter && disabledGetter(scope)) return;
        ev.stopPropagation();

        element.addClass('md-dragging');
        drag = {width: thumbContainer.prop('offsetWidth')};
      }

      function onDrag(ev) {
        if (!drag) return;
        ev.stopPropagation();
        ev.srcEvent && ev.srcEvent.preventDefault();

        var percent = ev.pointer.distanceX / drag.width;

        // if checked, start from right. else, start from left
        var translate = ngModel.$viewValue ?  1 + percent : percent;
        // Make sure the switch stays inside its bounds, 0-1%
        translate = Math.max(0, Math.min(1, translate));

        thumbContainer.css($mdConstant.CSS.TRANSFORM, 'translate3d(' + (100*translate) + '%,0,0)');
        drag.translate = translate;
      }

      function onDragEnd(ev) {
        if (!drag) return;
        ev.stopPropagation();

        element.removeClass('md-dragging');
        thumbContainer.css($mdConstant.CSS.TRANSFORM, '');

        // We changed if there is no distance (this is a click a click),
        // or if the drag distance is >50% of the total.
        var isChanged = ngModel.$viewValue ? drag.translate < 0.5 : drag.translate > 0.5;
        if (isChanged) {
          applyModelValue(!ngModel.$viewValue);
        }
        drag = null;

        // Wait for incoming mouse click
        scope.skipToggle = true;
        $timeout(function() {
          scope.skipToggle = false;
        }, 1);
      }

      function applyModelValue(newValue) {
        scope.$apply(function() {
          ngModel.$setViewValue(newValue);
          ngModel.$render();
        });
      }

    };
  }


}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.tabs
 * @description
 *
 *  Tabs, created with the `<md-tabs>` directive provide *tabbed* navigation with different styles.
 *  The Tabs component consists of clickable tabs that are aligned horizontally side-by-side.
 *
 *  Features include support for:
 *
 *  - static or dynamic tabs,
 *  - responsive designs,
 *  - accessibility support (ARIA),
 *  - tab pagination,
 *  - external or internal tab content,
 *  - focus indicators and arrow-key navigations,
 *  - programmatic lookup and access to tab controllers, and
 *  - dynamic transitions through different tab contents.
 *
 */
/*
 * @see js folder for tabs implementation
 */
angular.module('material.components.tabs', [
  'material.core',
  'material.components.icon'
]);

})();
(function(){
"use strict";

angular
.module('material.components.tabs')
.service('MdTabsPaginationService', MdTabsPaginationService);

/**
 * @private
 * @module material.components.tabs
 * @name MdTabsPaginationService
 * @description Provides many standalone functions to ease in pagination calculations.
 *
 * Most functions accept the elements and the current offset.
 *
 * The `elements` parameter is typically the value returned from the `getElements()` function of the
 * tabsController.
 *
 * The `offset` parameter is always positive regardless of LTR or RTL (we simply make the LTR one
 * negative when we apply our transform). This is typically the `ctrl.leftOffset` variable in the
 * tabsController.
 *
 * @returns MdTabsPaginationService
 * @constructor
 */
function MdTabsPaginationService() {
  return {
    decreasePageOffset: decreasePageOffset,
    increasePageOffset: increasePageOffset,
    getTabOffsets: getTabOffsets,
    getTotalTabsWidth: getTotalTabsWidth
  };

  /**
   * Returns the offset for the next decreasing page.
   *
   * @param elements
   * @param currentOffset
   * @returns {number}
   */
  function decreasePageOffset(elements, currentOffset) {
    var canvas       = elements.canvas,
        tabOffsets   = getTabOffsets(elements),
        i, firstVisibleTabOffset;

    // Find the first fully visible tab in offset range
    for (i = 0; i < tabOffsets.length; i++) {
      if (tabOffsets[i] >= currentOffset) {
        firstVisibleTabOffset = tabOffsets[i];
        break;
      }
    }

    // Return (the first visible tab offset - the tabs container width) without going negative
    return Math.max(0, firstVisibleTabOffset - canvas.clientWidth);
  }

  /**
   * Returns the offset for the next increasing page.
   *
   * @param elements
   * @param currentOffset
   * @returns {number}
   */
  function increasePageOffset(elements, currentOffset) {
    var canvas       = elements.canvas,
        maxOffset    = getTotalTabsWidth(elements) - canvas.clientWidth,
        tabOffsets   = getTabOffsets(elements),
        i, firstHiddenTabOffset;

    // Find the first partially (or fully) invisible tab
    for (i = 0; i < tabOffsets.length, tabOffsets[i] <= currentOffset + canvas.clientWidth; i++) {
      firstHiddenTabOffset = tabOffsets[i];
    }

    // Return the offset of the first hidden tab, or the maximum offset (whichever is smaller)
    return Math.min(maxOffset, firstHiddenTabOffset);
  }

  /**
   * Returns the offsets of all of the tabs based on their widths.
   *
   * @param elements
   * @returns {number[]}
   */
  function getTabOffsets(elements) {
    var i, tab, currentOffset = 0, offsets = [];

    for (i = 0; i < elements.tabs.length; i++) {
      tab = elements.tabs[i];
      offsets.push(currentOffset);
      currentOffset += tab.offsetWidth;
    }

    return offsets;
  }

  /**
   * Sum the width of all tabs.
   *
   * @param elements
   * @returns {number}
   */
  function getTotalTabsWidth(elements) {
    var sum = 0, i, tab;

    for (i = 0; i < elements.tabs.length; i++) {
      tab = elements.tabs[i];
      sum += tab.offsetWidth;
    }

    return sum;
  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc directive
 * @name mdTab
 * @module material.components.tabs
 *
 * @restrict E
 *
 * @description
 * The `<md-tab>` is a nested directive used within `<md-tabs>` to specify a tab with a **label**
 * and optional *view content*.
 *
 * If the `label` attribute is not specified, then an optional `<md-tab-label>` tag can be used to
 * specify more complex tab header markup. If neither the **label** nor the **md-tab-label** are
 * specified, then the nested markup of the `<md-tab>` is used as the tab header markup.
 *
 * Please note that if you use `<md-tab-label>`, your content **MUST** be wrapped in the
 * `<md-tab-body>` tag.  This is to define a clear separation between the tab content and the tab
 * label.
 *
 * This container is used by the TabsController to show/hide the active tab's content view. This
 * synchronization is automatically managed by the internal TabsController whenever the tab
 * selection changes. Selection changes can be initiated via data binding changes, programmatic
 * invocation, or user gestures.
 *
 * @param {string=} label Optional attribute to specify a simple string as the tab label
 * @param {boolean=} ng-disabled If present and expression evaluates to truthy, disabled tab
 *  selection.
 * @param {string=} md-tab-class Optional attribute to specify a class that will be applied to the
 *  tab's button
 * @param {expression=} md-on-deselect Expression to be evaluated after the tab has been
 *  de-selected.
 * @param {expression=} md-on-select Expression to be evaluated after the tab has been selected.
 * @param {boolean=} md-active When true, sets the active tab.  Note: There can only be one active
 *  tab at a time.
 *
 *
 * @usage
 *
 * <hljs lang="html">
 * <md-tab label="My Tab" md-tab-class="my-content-tab" ng-disabled md-on-select="onSelect()"
 *         md-on-deselect="onDeselect()">
 *   <h3>My Tab content</h3>
 * </md-tab>
 *
 * <md-tab>
 *   <md-tab-label>
 *     <h3>My Tab</h3>
 *   </md-tab-label>
 *   <md-tab-body>
 *     <p>
 *       Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque
 *       laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi
 *       architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit
 *       aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione
 *       voluptatem sequi nesciunt.
 *     </p>
 *   </md-tab-body>
 * </md-tab>
 * </hljs>
 *
 */
angular
    .module('material.components.tabs')
    .directive('mdTab', MdTab);

function MdTab () {
  return {
    require:  '^?mdTabs',
    terminal: true,
    compile:  function (element, attr) {
      var label = firstChild(element, 'md-tab-label'),
          body  = firstChild(element, 'md-tab-body');

      if (label.length === 0) {
        label = angular.element('<md-tab-label></md-tab-label>');
        if (attr.label) label.text(attr.label);
        else label.append(element.contents());

        if (body.length === 0) {
          var contents = element.contents().detach();
          body         = angular.element('<md-tab-body></md-tab-body>');
          body.append(contents);
        }
      }

      element.append(label);
      if (body.html()) element.append(body);

      return postLink;
    },
    scope:    {
      active:   '=?mdActive',
      disabled: '=?ngDisabled',
      select:   '&?mdOnSelect',
      deselect: '&?mdOnDeselect',
      tabClass: '@mdTabClass'
    }
  };

  function postLink (scope, element, attr, ctrl) {
    if (!ctrl) return;
    var index = ctrl.getTabElementIndex(element),
        body  = firstChild(element, 'md-tab-body').remove(),
        label = firstChild(element, 'md-tab-label').remove(),
        data  = ctrl.insertTab({
          scope:    scope,
          parent:   scope.$parent,
          index:    index,
          element:  element,
          template: body.html(),
          label:    label.html()
        }, index);

    scope.select   = scope.select || angular.noop;
    scope.deselect = scope.deselect || angular.noop;

    scope.$watch('active', function (active) { if (active) ctrl.select(data.getIndex(), true); });
    scope.$watch('disabled', function () { ctrl.refreshIndex(); });
    scope.$watch(
        function () {
          return ctrl.getTabElementIndex(element);
        },
        function (newIndex) {
          data.index = newIndex;
          ctrl.updateTabOrder();
        }
    );
    scope.$on('$destroy', function () { ctrl.removeTab(data); });
  }

  function firstChild (element, tagName) {
    var children = element[0].children;
    for (var i = 0, len = children.length; i < len; i++) {
      var child = children[i];
      if (child.tagName === tagName.toUpperCase()) return angular.element(child);
    }
    return angular.element();
  }
}

})();
(function(){
"use strict";

angular
    .module('material.components.tabs')
    .directive('mdTabItem', MdTabItem);

function MdTabItem () {
  return {
    require: '^?mdTabs',
    link:    function link (scope, element, attr, ctrl) {
      if (!ctrl) return;
      ctrl.attachRipple(scope, element);
    }
  };
}

})();
(function(){
"use strict";

angular
    .module('material.components.tabs')
    .directive('mdTabLabel', MdTabLabel);

function MdTabLabel () {
  return { terminal: true };
}


})();
(function(){
"use strict";


MdTabScroll.$inject = ["$parse"];angular.module('material.components.tabs')
    .directive('mdTabScroll', MdTabScroll);

function MdTabScroll ($parse) {
  return {
    restrict: 'A',
    compile: function ($element, attr) {
      var fn = $parse(attr.mdTabScroll, null, true);
      return function ngEventHandler (scope, element) {
        element.on('wheel', function (event) {
          scope.$apply(function () { fn(scope, { $event: event }); });
        });
      };
    }
  };
}

})();
(function(){
"use strict";


MdTabsController.$inject = ["$scope", "$element", "$window", "$mdConstant", "$mdTabInkRipple", "$mdUtil", "$animateCss", "$attrs", "$compile", "$mdTheming", "$mdInteraction", "$timeout", "MdTabsPaginationService"];angular
    .module('material.components.tabs')
    .controller('MdTabsController', MdTabsController);

/**
 * @ngInject
 */
function MdTabsController ($scope, $element, $window, $mdConstant, $mdTabInkRipple, $mdUtil,
                           $animateCss, $attrs, $compile, $mdTheming, $mdInteraction, $timeout,
                           MdTabsPaginationService) {
  // define private properties
  var ctrl      = this,
      locked    = false,
      queue     = [],
      destroyed = false,
      loaded    = false;

  // Define public methods
  ctrl.$onInit            = $onInit;
  ctrl.updatePagination   = $mdUtil.debounce(updatePagination, 100);
  ctrl.redirectFocus      = redirectFocus;
  ctrl.attachRipple       = attachRipple;
  ctrl.insertTab          = insertTab;
  ctrl.removeTab          = removeTab;
  ctrl.select             = select;
  ctrl.scroll             = scroll;
  ctrl.nextPage           = nextPage;
  ctrl.previousPage       = previousPage;
  ctrl.keydown            = keydown;
  ctrl.canPageForward     = canPageForward;
  ctrl.canPageBack        = canPageBack;
  ctrl.refreshIndex       = refreshIndex;
  ctrl.incrementIndex     = incrementIndex;
  ctrl.getTabElementIndex = getTabElementIndex;
  ctrl.updateInkBarStyles = $mdUtil.debounce(updateInkBarStyles, 100);
  ctrl.updateTabOrder     = $mdUtil.debounce(updateTabOrder, 100);
  ctrl.getFocusedTabId    = getFocusedTabId;

  // For AngularJS 1.4 and older, where there are no lifecycle hooks but bindings are pre-assigned,
  // manually call the $onInit hook.
  if (angular.version.major === 1 && angular.version.minor <= 4) {
    this.$onInit();
  }

  /**
   * AngularJS Lifecycle hook for newer AngularJS versions.
   * Bindings are not guaranteed to have been assigned in the controller, but they are in the
   * $onInit hook.
   */
  function $onInit() {
    // Define one-way bindings
    defineOneWayBinding('stretchTabs', handleStretchTabs);

    // Define public properties with change handlers
    defineProperty('focusIndex', handleFocusIndexChange, ctrl.selectedIndex || 0);
    defineProperty('offsetLeft', handleOffsetChange, 0);
    defineProperty('hasContent', handleHasContent, false);
    defineProperty('maxTabWidth', handleMaxTabWidth, getMaxTabWidth());
    defineProperty('shouldPaginate', handleShouldPaginate, false);

    // Define boolean attributes
    defineBooleanAttribute('noInkBar', handleInkBar);
    defineBooleanAttribute('dynamicHeight', handleDynamicHeight);
    defineBooleanAttribute('noPagination');
    defineBooleanAttribute('swipeContent');
    // TODO remove noDisconnect in 1.2.0
    defineBooleanAttribute('noDisconnect');
    defineBooleanAttribute('autoselect');
    defineBooleanAttribute('noSelectClick');
    defineBooleanAttribute('centerTabs', handleCenterTabs);
    defineBooleanAttribute('enableDisconnect');

    // Define public properties
    ctrl.scope             = $scope;
    ctrl.parent            = $scope.$parent;
    ctrl.tabs              = [];
    ctrl.lastSelectedIndex = null;
    ctrl.hasFocus          = false;
    ctrl.styleTabItemFocus = false;
    ctrl.shouldCenterTabs  = shouldCenterTabs();
    ctrl.tabContentPrefix  = 'tab-content-';
    ctrl.navigationHint = 'Use the left and right arrow keys to navigate between tabs';

    // Setup the tabs controller after all bindings are available.
    setupTabsController();
  }

  /**
   * Perform setup for the controller, setup events and watcher(s)
   */
  function setupTabsController () {
    ctrl.selectedIndex = ctrl.selectedIndex || 0;
    compileTemplate();
    configureWatchers();
    bindEvents();
    $mdTheming($element);
    $mdUtil.nextTick(function () {
      updateHeightFromContent();
      adjustOffset();
      updateInkBarStyles();
      ctrl.tabs[ ctrl.selectedIndex ] && ctrl.tabs[ ctrl.selectedIndex ].scope.select();
      loaded = true;
      updatePagination();
    });
  }

  /**
   * Compiles the template provided by the user.  This is passed as an attribute from the tabs
   * directive's template function.
   */
  function compileTemplate () {
    var template = $attrs.$mdTabsTemplate,
        element  = angular.element($element[0].querySelector('md-tab-data'));

    element.html(template);
    $compile(element.contents())(ctrl.parent);
    delete $attrs.$mdTabsTemplate;
  }

  /**
   * Binds events used by the tabs component.
   */
  function bindEvents () {
    angular.element($window).on('resize', handleWindowResize);
    $scope.$on('$destroy', cleanup);
  }

  /**
   * Configure watcher(s) used by Tabs
   */
  function configureWatchers () {
    $scope.$watch('$mdTabsCtrl.selectedIndex', handleSelectedIndexChange);
  }

  /**
   * Creates a one-way binding manually rather than relying on AngularJS's isolated scope
   * @param key
   * @param handler
   */
  function defineOneWayBinding (key, handler) {
    var attr = $attrs.$normalize('md-' + key);
    if (handler) defineProperty(key, handler);
    $attrs.$observe(attr, function (newValue) { ctrl[ key ] = newValue; });
  }

  /**
   * Defines boolean attributes with default value set to true. I.e. md-stretch-tabs with no value
   * will be treated as being truthy.
   * @param {string} key
   * @param {Function=} handler
   */
  function defineBooleanAttribute (key, handler) {
    var attr = $attrs.$normalize('md-' + key);
    if (handler) defineProperty(key, handler, undefined);
    if ($attrs.hasOwnProperty(attr)) updateValue($attrs[attr]);
    $attrs.$observe(attr, updateValue);
    function updateValue (newValue) {
      ctrl[ key ] = newValue !== 'false';
    }
  }

  /**
   * Remove any events defined by this controller
   */
  function cleanup () {
    destroyed = true;
    angular.element($window).off('resize', handleWindowResize);
  }

  // Change handlers

  /**
   * Toggles stretch tabs class and updates inkbar when tab stretching changes.
   */
  function handleStretchTabs () {
    var elements = getElements();
    angular.element(elements.wrapper).toggleClass('md-stretch-tabs', shouldStretchTabs());
    updateInkBarStyles();
  }

  /**
   * Update the value of ctrl.shouldCenterTabs.
   */
  function handleCenterTabs () {
    ctrl.shouldCenterTabs = shouldCenterTabs();
  }

  /**
   * @param {number} newWidth new max tab width in pixels
   * @param {number} oldWidth previous max tab width in pixels
   */
  function handleMaxTabWidth (newWidth, oldWidth) {
    if (newWidth !== oldWidth) {
      var elements = getElements();

      // Set the max width for the real tabs
      angular.forEach(elements.tabs, function(tab) {
        tab.style.maxWidth = newWidth + 'px';
      });

      // Set the max width for the dummy tabs too
      angular.forEach(elements.dummies, function(tab) {
        tab.style.maxWidth = newWidth + 'px';
      });

      $mdUtil.nextTick(ctrl.updateInkBarStyles);
    }
  }

  function handleShouldPaginate (newValue, oldValue) {
    if (newValue !== oldValue) {
      ctrl.maxTabWidth      = getMaxTabWidth();
      ctrl.shouldCenterTabs = shouldCenterTabs();
      $mdUtil.nextTick(function () {
        ctrl.maxTabWidth = getMaxTabWidth();
        adjustOffset(ctrl.selectedIndex);
      });
    }
  }

  /**
   * Add/remove the `md-no-tab-content` class depending on `ctrl.hasContent`
   * @param {boolean} hasContent
   */
  function handleHasContent (hasContent) {
    $element[ hasContent ? 'removeClass' : 'addClass' ]('md-no-tab-content');
  }

  /**
   * Apply ctrl.offsetLeft to the paging element when it changes
   * @param {string|number} left
   */
  function handleOffsetChange (left) {
    var newValue = ((ctrl.shouldCenterTabs || isRtl() ? '' : '-') + left + 'px');

    // Fix double-negative which can happen with RTL support
    newValue = newValue.replace('--', '');

    angular.element(getElements().paging).css($mdConstant.CSS.TRANSFORM,
                                              'translate(' + newValue + ', 0)');
    $scope.$broadcast('$mdTabsPaginationChanged');
  }

  /**
   * Update the UI whenever `ctrl.focusIndex` is updated
   * @param {number} newIndex
   * @param {number} oldIndex
   */
  function handleFocusIndexChange (newIndex, oldIndex) {
    if (newIndex === oldIndex) return;
    if (!getElements().tabs[ newIndex ]) return;
    adjustOffset();
    redirectFocus();
  }

  /**
   * Update the UI whenever the selected index changes. Calls user-defined select/deselect methods.
   * @param {number} newValue selected index's new value
   * @param {number} oldValue selected index's previous value
   */
  function handleSelectedIndexChange (newValue, oldValue) {
    if (newValue === oldValue) return;

    ctrl.selectedIndex     = getNearestSafeIndex(newValue);
    ctrl.lastSelectedIndex = oldValue;
    ctrl.updateInkBarStyles();
    updateHeightFromContent();
    adjustOffset(newValue);
    $scope.$broadcast('$mdTabsChanged');
    ctrl.tabs[ oldValue ] && ctrl.tabs[ oldValue ].scope.deselect();
    ctrl.tabs[ newValue ] && ctrl.tabs[ newValue ].scope.select();
  }

  function getTabElementIndex(tabEl){
    var tabs = $element[0].getElementsByTagName('md-tab');
    return Array.prototype.indexOf.call(tabs, tabEl[0]);
  }

  /**
   * Queues up a call to `handleWindowResize` when a resize occurs while the tabs component is
   * hidden.
   */
  function handleResizeWhenVisible () {
    // if there is already a watcher waiting for resize, do nothing
    if (handleResizeWhenVisible.watcher) return;
    // otherwise, we will abuse the $watch function to check for visible
    handleResizeWhenVisible.watcher = $scope.$watch(function () {
      // since we are checking for DOM size, we use $mdUtil.nextTick() to wait for after the DOM updates
      $mdUtil.nextTick(function () {
        // if the watcher has already run (ie. multiple digests in one cycle), do nothing
        if (!handleResizeWhenVisible.watcher) return;

        if ($element.prop('offsetParent')) {
          handleResizeWhenVisible.watcher();
          handleResizeWhenVisible.watcher = null;

          handleWindowResize();
        }
      }, false);
    });
  }

  // Event handlers / actions

  /**
   * Handle user keyboard interactions
   * @param {KeyboardEvent} event keydown event
   */
  function keydown (event) {
    switch (event.keyCode) {
      case $mdConstant.KEY_CODE.LEFT_ARROW:
        event.preventDefault();
        incrementIndex(-1, true);
        break;
      case $mdConstant.KEY_CODE.RIGHT_ARROW:
        event.preventDefault();
        incrementIndex(1, true);
        break;
      case $mdConstant.KEY_CODE.SPACE:
      case $mdConstant.KEY_CODE.ENTER:
        event.preventDefault();
        if (!locked) select(ctrl.focusIndex);
        break;
      case $mdConstant.KEY_CODE.TAB:
        // On tabbing out of the tablist, reset hasFocus to reset ng-focused and
        // its md-focused class if the focused tab is not the active tab.
        if (ctrl.focusIndex !== ctrl.selectedIndex) {
          ctrl.focusIndex = ctrl.selectedIndex;
        }
        break;
    }
  }

  /**
   * Update the selected index. Triggers a click event on the original `md-tab` element in order
   * to fire user-added click events if canSkipClick or `md-no-select-click` are false.
   * @param index
   * @param canSkipClick Optionally allow not firing the click event if `md-no-select-click` is also true.
   */
  function select (index, canSkipClick) {
    if (!locked) ctrl.focusIndex = ctrl.selectedIndex = index;
    // skip the click event if noSelectClick is enabled
    if (canSkipClick && ctrl.noSelectClick) return;
    // nextTick is required to prevent errors in user-defined click events
    $mdUtil.nextTick(function () {
      ctrl.tabs[ index ].element.triggerHandler('click');
    }, false);
  }

  /**
   * When pagination is on, this makes sure the selected index is in view.
   * @param {WheelEvent} event
   */
  function scroll (event) {
    if (!ctrl.shouldPaginate) return;
    event.preventDefault();
    if (event.deltaY) {
      ctrl.offsetLeft = fixOffset(ctrl.offsetLeft + event.deltaY);
    } else if (event.deltaX) {
      ctrl.offsetLeft = fixOffset(ctrl.offsetLeft + event.deltaX);
    }
  }

  /**
   * Slides the tabs over approximately one page forward.
   */
  function nextPage () {
    if (!ctrl.canPageForward()) { return; }

    var newOffset = MdTabsPaginationService.increasePageOffset(getElements(), ctrl.offsetLeft);

    ctrl.offsetLeft = fixOffset(newOffset);
  }

  /**
   * Slides the tabs over approximately one page backward.
   */
  function previousPage () {
    if (!ctrl.canPageBack()) { return; }

    var newOffset = MdTabsPaginationService.decreasePageOffset(getElements(), ctrl.offsetLeft);

    // Set the new offset
    ctrl.offsetLeft = fixOffset(newOffset);
  }

  /**
   * Update size calculations when the window is resized.
   */
  function handleWindowResize () {
    ctrl.lastSelectedIndex = ctrl.selectedIndex;
    ctrl.offsetLeft        = fixOffset(ctrl.offsetLeft);

    $mdUtil.nextTick(function () {
      ctrl.updateInkBarStyles();
      updatePagination();
    });
  }

  /**
   * Hides or shows the tabs ink bar.
   * @param {boolean} hide A Boolean (not just truthy/falsy) value to determine whether the class
   * should be added or removed.
   */
  function handleInkBar (hide) {
    angular.element(getElements().inkBar).toggleClass('ng-hide', hide);
  }

  /**
   * Enables or disables tabs dynamic height.
   * @param {boolean} value A Boolean (not just truthy/falsy) value to determine whether the class
   * should be added or removed.
   */
  function handleDynamicHeight (value) {
    $element.toggleClass('md-dynamic-height', value);
  }

  /**
   * Remove a tab from the data and select the nearest valid tab.
   * @param {Object} tabData tab to remove
   */
  function removeTab (tabData) {
    if (destroyed) return;
    var selectedIndex = ctrl.selectedIndex,
        tab           = ctrl.tabs.splice(tabData.getIndex(), 1)[ 0 ];
    refreshIndex();
    // when removing a tab, if the selected index did not change, we have to manually trigger the
    //   tab select/deselect events
    if (ctrl.selectedIndex === selectedIndex) {
      tab.scope.deselect();
      ctrl.tabs[ ctrl.selectedIndex ] && ctrl.tabs[ ctrl.selectedIndex ].scope.select();
    }
    $mdUtil.nextTick(function () {
      updatePagination();
      ctrl.offsetLeft = fixOffset(ctrl.offsetLeft);
    });
  }

  /**
   * Create an entry in the tabs array for a new tab at the specified index.
   * @param {Object} tabData tab to insert
   * @param {number} index location to insert the new tab
   * @returns {Object} the inserted tab
   */
  function insertTab (tabData, index) {
    var hasLoaded = loaded;
    var proto = {
          getIndex:     function () { return ctrl.tabs.indexOf(tab); },
          isActive:     function () { return this.getIndex() === ctrl.selectedIndex; },
          isLeft:       function () { return this.getIndex() < ctrl.selectedIndex; },
          isRight:      function () { return this.getIndex() > ctrl.selectedIndex; },
          // TODO remove reference to noDisconnect in 1.2.0
          shouldRender: function () { return !ctrl.noDisconnect || this.isActive(); },
          hasFocus:     function () {
            return ctrl.styleTabItemFocus
                && ctrl.hasFocus && this.getIndex() === ctrl.focusIndex;
          },
          id:           $mdUtil.nextUid(),
          hasContent: !!(tabData.template && tabData.template.trim())
    };
    var tab = angular.extend(proto, tabData);

    if (angular.isDefined(index)) {
      ctrl.tabs.splice(index, 0, tab);
    } else {
      ctrl.tabs.push(tab);
    }
    processQueue();
    updateHasContent();

    $mdUtil.nextTick(function () {
      updatePagination();
      setAriaControls(tab);

      // if autoselect is enabled, select the newly added tab
      if (hasLoaded && ctrl.autoselect) {
        $mdUtil.nextTick(function () {
          $mdUtil.nextTick(function () { select(ctrl.tabs.indexOf(tab)); });
        });
      }
    });
    return tab;
  }

  // Getter methods

  /**
   * Gathers references to all of the DOM elements used by this controller.
   * @returns {Object}
   */
  function getElements () {
    var elements = {};
    var node = $element[0];

    // gather tab bar elements
    elements.wrapper = node.querySelector('md-tabs-wrapper');
    elements.canvas  = elements.wrapper.querySelector('md-tabs-canvas');
    elements.paging  = elements.canvas.querySelector('md-pagination-wrapper');
    elements.inkBar  = elements.paging.querySelector('md-ink-bar');
    elements.nextButton = node.querySelector('md-next-button');
    elements.prevButton = node.querySelector('md-prev-button');

    elements.contents = node.querySelectorAll('md-tabs-content-wrapper > md-tab-content');
    elements.tabs    = elements.paging.querySelectorAll('md-tab-item');
    elements.dummies = elements.canvas.querySelectorAll('md-dummy-tab');

    return elements;
  }

  /**
   * Determines whether or not the left pagination arrow should be enabled.
   * @returns {boolean}
   */
  function canPageBack () {
    // This works for both LTR and RTL
    return ctrl.offsetLeft > 0;
  }

  /**
   * Determines whether or not the right pagination arrow should be enabled.
   * @returns {*|boolean}
   */
  function canPageForward () {
    var elements = getElements();
    var lastTab = elements.tabs[ elements.tabs.length - 1 ];

    if (isRtl()) {
      return ctrl.offsetLeft < elements.paging.offsetWidth - elements.canvas.offsetWidth;
    }

    return lastTab && lastTab.offsetLeft + lastTab.offsetWidth > elements.canvas.clientWidth +
        ctrl.offsetLeft;
  }

  /**
   * Returns currently focused tab item's element ID
   */
  function getFocusedTabId() {
    var focusedTab = ctrl.tabs[ctrl.focusIndex];
    if (!focusedTab || !focusedTab.id) {
      return null;
    }
    return 'tab-item-' + focusedTab.id;
  }

  /**
   * Determines if the UI should stretch the tabs to fill the available space.
   * @returns {*}
   */
  function shouldStretchTabs () {
    switch (ctrl.stretchTabs) {
      case 'always':
        return true;
      case 'never':
        return false;
      default:
        return !ctrl.shouldPaginate
            && $window.matchMedia('(max-width: 600px)').matches;
    }
  }

  /**
   * Determines if the tabs should appear centered.
   * @returns {boolean}
   */
  function shouldCenterTabs () {
    return ctrl.centerTabs && !ctrl.shouldPaginate;
  }

  /**
   * Determines if pagination is necessary to display the tabs within the available space.
   * @returns {boolean} true if pagination is necessary, false otherwise
   */
  function shouldPaginate () {
    var shouldPaginate;
    if (ctrl.noPagination || !loaded) return false;
    var canvasWidth = $element.prop('clientWidth');

    angular.forEach(getElements().tabs, function (tab) {
      canvasWidth -= tab.offsetWidth;
    });

    shouldPaginate = canvasWidth < 0;
    // Work around width calculation issues on IE11 when pagination is enabled.
    // Don't do this on other browsers because it breaks scroll to new tab animation.
    if ($mdUtil.msie) {
      if (shouldPaginate) {
        getElements().paging.style.width = '999999px';
      } else {
        getElements().paging.style.width = undefined;
      }
    }
    return shouldPaginate;
  }

  /**
   * Finds the nearest tab index that is available. This is primarily used for when the active
   * tab is removed.
   * @param newIndex
   * @returns {*}
   */
  function getNearestSafeIndex (newIndex) {
    if (newIndex === -1) return -1;
    var maxOffset = Math.max(ctrl.tabs.length - newIndex, newIndex),
        i, tab;
    for (i = 0; i <= maxOffset; i++) {
      tab = ctrl.tabs[ newIndex + i ];
      if (tab && (tab.scope.disabled !== true)) return tab.getIndex();
      tab = ctrl.tabs[ newIndex - i ];
      if (tab && (tab.scope.disabled !== true)) return tab.getIndex();
    }
    return newIndex;
  }

  // Utility methods

  /**
   * Defines a property using a getter and setter in order to trigger a change handler without
   * using `$watch` to observe changes.
   * @param {PropertyKey} key
   * @param {Function} handler
   * @param {any} value
   */
  function defineProperty (key, handler, value) {
    Object.defineProperty(ctrl, key, {
      get: function () { return value; },
      set: function (newValue) {
        var oldValue = value;
        value        = newValue;
        handler && handler(newValue, oldValue);
      }
    });
  }

  /**
   * Updates whether or not pagination should be displayed.
   */
  function updatePagination () {
    ctrl.maxTabWidth = getMaxTabWidth();
    ctrl.shouldPaginate = shouldPaginate();
  }

  /**
   * @param {Array<HTMLElement>} tabs tab item elements for use in computing total width
   * @returns {number} the width of the tabs in the specified array in pixels
   */
  function calcTabsWidth(tabs) {
    var width = 0;

    angular.forEach(tabs, function (tab) {
      // Uses the larger value between `getBoundingClientRect().width` and `offsetWidth`.  This
      // prevents `offsetWidth` value from being rounded down and causing wrapping issues, but
      // also handles scenarios where `getBoundingClientRect()` is inaccurate (ie. tabs inside
      // of a dialog).
      width += Math.max(tab.offsetWidth, tab.getBoundingClientRect().width);
    });

    return Math.ceil(width);
  }

  /**
   * @returns {number} either the max width as constrained by the container or the max width from
   * the 2017 version of the Material Design spec.
   */
  function getMaxTabWidth() {
    var elements = getElements(),
      containerWidth = elements.canvas.clientWidth,

      // See https://material.io/archive/guidelines/components/tabs.html#tabs-specs
      specMax = 264;

    // Do the spec maximum, or the canvas width; whichever is *smaller* (tabs larger than the canvas
    // width can break the pagination) but not less than 0
    return Math.max(0, Math.min(containerWidth - 1, specMax));
  }

  /**
   * Re-orders the tabs and updates the selected and focus indexes to their new positions.
   * This is triggered by `tabDirective.js` when the user's tabs have been re-ordered.
   */
  function updateTabOrder () {
    var selectedItem   = ctrl.tabs[ ctrl.selectedIndex ],
        focusItem      = ctrl.tabs[ ctrl.focusIndex ];
    ctrl.tabs          = ctrl.tabs.sort(function (a, b) {
      return a.index - b.index;
    });
    ctrl.selectedIndex = ctrl.tabs.indexOf(selectedItem);
    ctrl.focusIndex    = ctrl.tabs.indexOf(focusItem);
  }

  /**
   * This moves the selected or focus index left or right. This is used by the keydown handler.
   * @param {number} inc amount to increment
   * @param {boolean} focus true to increment the focus index, false to increment the selected index
   */
  function incrementIndex (inc, focus) {
    var newIndex,
        key   = focus ? 'focusIndex' : 'selectedIndex',
        index = ctrl[ key ];
    for (newIndex = index + inc;
         ctrl.tabs[ newIndex ] && ctrl.tabs[ newIndex ].scope.disabled;
         newIndex += inc) { /* do nothing */ }

    newIndex = (index + inc + ctrl.tabs.length) % ctrl.tabs.length;

    if (ctrl.tabs[ newIndex ]) {
      ctrl[ key ] = newIndex;
    }
  }

  /**
   * This is used to forward focus to tab container elements. This method is necessary to avoid
   * animation issues when attempting to focus an item that is out of view.
   */
  function redirectFocus () {
    ctrl.styleTabItemFocus = ($mdInteraction.getLastInteractionType() === 'keyboard');
    var tabToFocus = getElements().tabs[ctrl.focusIndex];
    if (tabToFocus) {
      tabToFocus.focus();
    }
  }

  /**
   * Forces the pagination to move the focused tab into view.
   * @param {number=} index of tab to have its offset adjusted
   */
  function adjustOffset (index) {
    var elements = getElements();

    if (!angular.isNumber(index)) index = ctrl.focusIndex;
    if (!elements.tabs[ index ]) return;
    if (ctrl.shouldCenterTabs) return;
    var tab         = elements.tabs[ index ],
        left        = tab.offsetLeft,
        right       = tab.offsetWidth + left,
        extraOffset = 32;

    // If we are selecting the first tab (in LTR and RTL), always set the offset to 0
    if (index === 0) {
      ctrl.offsetLeft = 0;
      return;
    }

    if (isRtl()) {
      var tabWidthsBefore = calcTabsWidth(Array.prototype.slice.call(elements.tabs, 0, index));
      var tabWidthsIncluding = calcTabsWidth(Array.prototype.slice.call(elements.tabs, 0, index + 1));

      ctrl.offsetLeft = Math.min(ctrl.offsetLeft, fixOffset(tabWidthsBefore));
      ctrl.offsetLeft = Math.max(ctrl.offsetLeft, fixOffset(tabWidthsIncluding - elements.canvas.clientWidth));
    } else {
      ctrl.offsetLeft = Math.max(ctrl.offsetLeft, fixOffset(right - elements.canvas.clientWidth + extraOffset));
      ctrl.offsetLeft = Math.min(ctrl.offsetLeft, fixOffset(left));
    }
  }

  /**
   * Iterates through all queued functions and clears the queue. This is used for functions that
   * are called before the UI is ready, such as size calculations.
   */
  function processQueue () {
    queue.forEach(function (func) { $mdUtil.nextTick(func); });
    queue = [];
  }

  /**
   * Determines if the tab content area is needed.
   */
  function updateHasContent () {
    var hasContent = false;
    var i;

    for (i = 0; i < ctrl.tabs.length; i++) {
      if (ctrl.tabs[i].hasContent) {
        hasContent = true;
        break;
      }
    }

    ctrl.hasContent = hasContent;
  }

  /**
   * Moves the indexes to their nearest valid values.
   */
  function refreshIndex () {
    ctrl.selectedIndex = getNearestSafeIndex(ctrl.selectedIndex);
    ctrl.focusIndex    = getNearestSafeIndex(ctrl.focusIndex);
  }

  /**
   * Calculates the content height of the current tab.
   * @returns {*}
   */
  function updateHeightFromContent () {
    if (!ctrl.dynamicHeight) return $element.css('height', '');
    if (!ctrl.tabs.length) return queue.push(updateHeightFromContent);

    var elements = getElements();

    var tabContent    = elements.contents[ ctrl.selectedIndex ],
        contentHeight = tabContent ? tabContent.offsetHeight : 0,
        tabsHeight    = elements.wrapper.offsetHeight,
        newHeight     = contentHeight + tabsHeight,
        currentHeight = $element.prop('clientHeight');

    if (currentHeight === newHeight) return;

    // Adjusts calculations for when the buttons are bottom-aligned since this relies on absolute
    // positioning.  This should probably be cleaned up if a cleaner solution is possible.
    if ($element.attr('md-align-tabs') === 'bottom') {
      currentHeight -= tabsHeight;
      newHeight -= tabsHeight;
      // Need to include bottom border in these calculations
      if ($element.attr('md-border-bottom') !== undefined) {
        ++currentHeight;
      }
    }

    // Lock during animation so the user can't change tabs
    locked = true;

    var fromHeight = { height: currentHeight + 'px' },
        toHeight = { height: newHeight + 'px' };

    // Set the height to the current, specific pixel height to fix a bug on iOS where the height
    // first animates to 0, then back to the proper height causing a visual glitch
    $element.css(fromHeight);

    // Animate the height from the old to the new
    $animateCss($element, {
      from: fromHeight,
      to: toHeight,
      easing: 'cubic-bezier(0.35, 0, 0.25, 1)',
      duration: 0.5
    }).start().done(function () {
      // Then (to fix the same iOS issue as above), disable transitions and remove the specific
      // pixel height so the height can size with browser width/content changes, etc.
      $element.css({
        transition: 'none',
        height: ''
      });

      // In the next tick, re-allow transitions (if we do it all at once, $element.css is "smart"
      // enough to batch it for us instead of doing it immediately, which undoes the original
      // transition: none)
      $mdUtil.nextTick(function() {
        $element.css('transition', '');
      });

      // And unlock so tab changes can occur
      locked = false;
    });
  }

  /**
   * Repositions the ink bar to the selected tab.
   * Parameters are used when calling itself recursively when md-center-tabs is used as we need to
   * run two passes to properly center the tabs. These parameters ensure that we only run two passes
   * and that we don't run indefinitely.
   * @param {number=} previousTotalWidth previous width of pagination wrapper
   * @param {number=} previousWidthOfTabItems previous width of all tab items
   */
  function updateInkBarStyles (previousTotalWidth, previousWidthOfTabItems) {
    if (ctrl.noInkBar) {
      return;
    }
    var elements = getElements();

    if (!elements.tabs[ ctrl.selectedIndex ]) {
      angular.element(elements.inkBar).css({ left: 'auto', right: 'auto' });
      return;
    }

    if (!ctrl.tabs.length) {
      queue.push(ctrl.updateInkBarStyles);
      return;
    }
    // If the element is not visible, we will not be able to calculate sizes until it becomes
    // visible. We should treat that as a resize event rather than just updating the ink bar.
    if (!$element.prop('offsetParent')) {
      handleResizeWhenVisible();
      return;
    }

    var index      = ctrl.selectedIndex,
        totalWidth = elements.paging.offsetWidth,
        tab        = elements.tabs[ index ],
        left       = tab.offsetLeft,
        right      = totalWidth - left - tab.offsetWidth;

    if (ctrl.shouldCenterTabs) {
      // We need to use the same calculate process as in the pagination wrapper, to avoid rounding
      // deviations.
      var totalWidthOfTabItems = calcTabsWidth(elements.tabs);

      if (totalWidth > totalWidthOfTabItems &&
          previousTotalWidth !== totalWidth &&
          previousWidthOfTabItems !== totalWidthOfTabItems) {
        $timeout(updateInkBarStyles, 0, true, totalWidth, totalWidthOfTabItems);
      }
    }
    updateInkBarClassName();
    angular.element(elements.inkBar).css({ left: left + 'px', right: right + 'px' });
  }

  /**
   * Adds left/right classes so that the ink bar will animate properly.
   */
  function updateInkBarClassName () {
    var elements = getElements();
    var newIndex = ctrl.selectedIndex,
        oldIndex = ctrl.lastSelectedIndex,
        ink      = angular.element(elements.inkBar);
    if (!angular.isNumber(oldIndex)) return;
    ink
        .toggleClass('md-left', newIndex < oldIndex)
        .toggleClass('md-right', newIndex > oldIndex);
  }

  /**
   * Takes an offset value and makes sure that it is within the min/max allowed values.
   * @param {number} value
   * @returns {number}
   */
  function fixOffset (value) {
    var elements = getElements();

    if (!elements.tabs.length || !ctrl.shouldPaginate) return 0;

    var lastTab    = elements.tabs[ elements.tabs.length - 1 ],
        totalWidth = lastTab.offsetLeft + lastTab.offsetWidth;

    if (isRtl()) {
      value = Math.min(elements.paging.offsetWidth - elements.canvas.clientWidth, value);
      value = Math.max(0, value);
    } else {
      value = Math.max(0, value);
      value = Math.min(totalWidth - elements.canvas.clientWidth, value);
    }

    return value;
  }

  /**
   * Attaches a ripple to the tab item element.
   * @param scope
   * @param element
   */
  function attachRipple (scope, element) {
    var elements = getElements();
    var options = { colorElement: angular.element(elements.inkBar) };
    $mdTabInkRipple.attach(scope, element, options);
  }

  /**
   * Sets the `aria-controls` attribute to the elements that correspond to the passed-in tab.
   * @param tab
   */
  function setAriaControls (tab) {
    if (tab.hasContent) {
      var nodes = $element[0].querySelectorAll('[md-tab-id="' + tab.id + '"]');
      angular.element(nodes).attr('aria-controls', ctrl.tabContentPrefix + tab.id);
    }
  }

  function isRtl() {
    return $mdUtil.isRtl($attrs);
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc directive
 * @name mdTabs
 * @module material.components.tabs
 *
 * @restrict E
 *
 * @description
 * The `<md-tabs>` directive serves as the container for 1..n
 * <a ng-href="api/directive/mdTab">`<md-tab>`</a> child directives.
 * In turn, the nested `<md-tab>` directive is used to specify a tab label for the
 * **header button** and <i>optional</i> tab view content that will be associated with each tab
 * button.
 *
 * Below is the markup for its simplest usage:
 *
 *  <hljs lang="html">
 *  <md-tabs>
 *    <md-tab label="Tab #1"></md-tab>
 *    <md-tab label="Tab #2"></md-tab>
 *    <md-tab label="Tab #3"></md-tab>
 *  </md-tabs>
 *  </hljs>
 *
 * Tabs support three (3) usage scenarios:
 *
 *  1. Tabs (buttons only)
 *  2. Tabs with internal view content
 *  3. Tabs with external view content
 *
 * **Tabs-only** support is useful when tab buttons are used for custom navigation regardless of any
 * other components, content, or views.
 *
 * <i><b>Note:</b> If you are using the Tabs component for page-level navigation, please use
 * the <a ng-href="./api/directive/mdNavBar">NavBar component</a> instead. It handles this
 * case a more natively and more performantly.</i>
 *
 * **Tabs with internal views** are the traditional usage where each tab has associated view
 * content and the view switching is managed internally by the Tabs component.
 *
 * **Tabs with external view content** is often useful when content associated with each tab is
 * independently managed and data-binding notifications announce tab selection changes.
 *
 * Additional features also include:
 *
 * *  Content can include any markup.
 * *  If a tab is disabled while active/selected, then the next tab will be auto-selected.
 *
 * ### Explanation of tab stretching
 *
 * Initially, tabs will have an inherent size.  This size will either be defined by how much space
 * is needed to accommodate their text or set by the user through CSS.
 * Calculations will be based on this size.
 *
 * On mobile devices, tabs will be expanded to fill the available horizontal space.
 * When this happens, all tabs will become the same size.
 *
 * On desktops, by default, stretching will never occur.
 *
 * This default behavior can be overridden through the `md-stretch-tabs` attribute.
 * Here is a table showing when stretching will occur:
 *
 * `md-stretch-tabs` | mobile    | desktop
 * ------------------|-----------|--------
 * `auto`            | stretched | ---
 * `always`          | stretched | stretched
 * `never`           | ---       | ---
 *
 * @param {number=} md-selected Index of the active/selected tab.
 * @param {expression=} md-no-ink-bar If `true` or no value, disables the selection ink bar.
 * @param {string=} md-align-tabs Attribute to indicate position of tab buttons: `bottom` or `top`;
 *  Default is `top`.
 * @param {string=} md-stretch-tabs Attribute to indicate whether or not to stretch tabs: `auto`,
 *  `always`, or `never`; Default is `auto`.
 * @param {expression=} md-dynamic-height If `true` or no value, the tab wrapper will resize based
 *  on the contents of the selected tab.
 * @param {boolean=} md-border-bottom If the attribute is present, shows a solid `1px` border
 *  between the tabs and their content.
 * @param {boolean=} md-center-tabs If the attribute is present, tabs will be centered provided
 *  there is no need for pagination.
 * @param {boolean=} md-no-pagination If the attribute is present, pagination will remain off.
 * @param {expression=} md-swipe-content When enabled, swipe gestures will be enabled for the content
 *  area to allow swiping between tabs.
 * @param {boolean=} md-enable-disconnect When enabled, scopes will be disconnected for tabs that
 *  are not being displayed. This provides a performance boost, but may also cause unexpected
 *  issues. It is not recommended for most users.
 * @param {boolean=} md-no-disconnect **Deprecated**: If your tab content has background tasks
 *  (ie. event listeners), you will want to include this to prevent the scope from being
 *  disconnected.
 * @param {boolean=} md-autoselect If the attribute is present, any tabs added after the initial
 *  load will be automatically selected.
 * @param {boolean=} md-no-select-click When true, click events will not be fired when the value of
 *  `md-active` on an `md-tab` changes. This is useful when using tabs with UI-Router's child
 *  states, as triggering a click event in that case can cause an extra tab change to occur.
 * @param {string=} md-navigation-hint Attribute to override the default `tablist` navigation hint
 *  that screen readers will announce to provide instructions for navigating between tabs. This is
 *  desirable when you want the hint to be in a different language. Default is "Use the left and
 *  right arrow keys to navigate between tabs".
 *
 * @usage
 * <hljs lang="html">
 * <md-tabs md-selected="selectedIndex" >
 *   <img ng-src="img/angular.png" class="centered">
 *   <md-tab
 *       ng-repeat="tab in tabs | orderBy:predicate:reversed"
 *       md-on-select="onTabSelected(tab)"
 *       md-on-deselect="announceDeselected(tab)"
 *       ng-disabled="tab.disabled">
 *     <md-tab-label>
 *       {{tab.title}}
 *       <img src="img/removeTab.png" ng-click="removeTab(tab)" class="delete">
 *     </md-tab-label>
 *     <md-tab-body>
 *       {{tab.content}}
 *     </md-tab-body>
 *   </md-tab>
 * </md-tabs>
 * </hljs>
 *
 */
MdTabs.$inject = ["$$mdSvgRegistry"];
angular
    .module('material.components.tabs')
    .directive('mdTabs', MdTabs);

function MdTabs ($$mdSvgRegistry) {
  return {
    scope:            {
      navigationHint: '@?mdNavigationHint',
      selectedIndex: '=?mdSelected'
    },
    template:         function (element, attr) {
      attr.$mdTabsTemplate = element.html();
      return '' +
        '<md-tabs-wrapper> ' +
          '<md-tab-data></md-tab-data> ' +
          '<md-prev-button ' +
              'tabindex="-1" ' +
              'role="button" ' +
              'aria-label="Previous Page" ' +
              'aria-disabled="{{!$mdTabsCtrl.canPageBack()}}" ' +
              'ng-class="{ \'md-disabled\': !$mdTabsCtrl.canPageBack() }" ' +
              'ng-if="$mdTabsCtrl.shouldPaginate" ' +
              'ng-click="$mdTabsCtrl.previousPage()"> ' +
            '<md-icon md-svg-src="'+ $$mdSvgRegistry.mdTabsArrow +'"></md-icon> ' +
          '</md-prev-button> ' +
          '<md-next-button ' +
              'tabindex="-1" ' +
              'role="button" ' +
              'aria-label="Next Page" ' +
              'aria-disabled="{{!$mdTabsCtrl.canPageForward()}}" ' +
              'ng-class="{ \'md-disabled\': !$mdTabsCtrl.canPageForward() }" ' +
              'ng-if="$mdTabsCtrl.shouldPaginate" ' +
              'ng-click="$mdTabsCtrl.nextPage()"> ' +
            '<md-icon md-svg-src="'+ $$mdSvgRegistry.mdTabsArrow +'"></md-icon> ' +
          '</md-next-button> ' +
          '<md-tabs-canvas ' +
              'tabindex="{{ $mdTabsCtrl.hasFocus ? -1 : 0 }}" ' +
              'ng-focus="$mdTabsCtrl.redirectFocus()" ' +
              'ng-class="{ ' +
                  '\'md-paginated\': $mdTabsCtrl.shouldPaginate, ' +
                  '\'md-center-tabs\': $mdTabsCtrl.shouldCenterTabs ' +
              '}" ' +
              'ng-keydown="$mdTabsCtrl.keydown($event)"> ' +
            '<md-pagination-wrapper ' +
                'ng-class="{ \'md-center-tabs\': $mdTabsCtrl.shouldCenterTabs }" ' +
                'md-tab-scroll="$mdTabsCtrl.scroll($event)" ' +
                'role="tablist" ' +
                'aria-label="{{::$mdTabsCtrl.navigationHint}}">' +
              '<md-tab-item ' +
                  'tabindex="{{ tab.isActive() ? 0 : -1 }}" ' +
                  'class="md-tab {{::tab.scope.tabClass}}" ' +
                  'ng-repeat="tab in $mdTabsCtrl.tabs" ' +
                  'role="tab" ' +
                  'id="tab-item-{{::tab.id}}" ' +
                  'md-tab-id="{{::tab.id}}" ' +
                  'aria-selected="{{tab.isActive()}}" ' +
                  'aria-disabled="{{tab.scope.disabled || \'false\'}}" ' +
                  'ng-click="$mdTabsCtrl.select(tab.getIndex())" ' +
                  'ng-focus="$mdTabsCtrl.hasFocus = true" ' +
                  'ng-blur="$mdTabsCtrl.hasFocus = false" ' +
                  'ng-class="{ ' +
                      '\'md-active\':    tab.isActive(), ' +
                      '\'md-focused\':   tab.hasFocus(), ' +
                      '\'md-disabled\':  tab.scope.disabled ' +
                  '}" ' +
                  'ng-disabled="tab.scope.disabled" ' +
                  'md-swipe-left="$mdTabsCtrl.nextPage()" ' +
                  'md-swipe-right="$mdTabsCtrl.previousPage()" ' +
                  'md-tabs-template="::tab.label" ' +
                  'md-scope="::tab.parent"></md-tab-item> ' +
              '<md-ink-bar></md-ink-bar> ' +
            '</md-pagination-wrapper> ' +
            '<md-tabs-dummy-wrapper aria-hidden="true" class="md-visually-hidden md-dummy-wrapper"> ' +
              '<md-dummy-tab ' +
                  'class="md-tab" ' +
                  'tabindex="-1" ' +
                  'ng-focus="$mdTabsCtrl.hasFocus = true" ' +
                  'ng-blur="$mdTabsCtrl.hasFocus = false" ' +
                  'ng-repeat="tab in $mdTabsCtrl.tabs" ' +
                  'md-tabs-template="::tab.label" ' +
                  'md-scope="::tab.parent"></md-dummy-tab> ' +
            '</md-tabs-dummy-wrapper> ' +
          '</md-tabs-canvas> ' +
        '</md-tabs-wrapper> ' +
        '<md-tabs-content-wrapper ng-show="$mdTabsCtrl.hasContent && $mdTabsCtrl.selectedIndex >= 0" class="_md"> ' +
          '<md-tab-content ' +
              'id="{{:: $mdTabsCtrl.tabContentPrefix + tab.id}}" ' +
              'class="_md" ' +
              'role="tabpanel" ' +
              'aria-labelledby="tab-item-{{::tab.id}}" ' +
              'md-swipe-left="$mdTabsCtrl.swipeContent && $mdTabsCtrl.incrementIndex(1)" ' +
              'md-swipe-right="$mdTabsCtrl.swipeContent && $mdTabsCtrl.incrementIndex(-1)" ' +
              'ng-if="tab.hasContent" ' +
              'ng-repeat="(index, tab) in $mdTabsCtrl.tabs" ' +
              'ng-class="{ ' +
                '\'md-no-transition\': $mdTabsCtrl.lastSelectedIndex == null, ' +
                '\'md-active\':        tab.isActive(), ' +
                '\'md-left\':          tab.isLeft(), ' +
                '\'md-right\':         tab.isRight(), ' +
                '\'md-no-scroll\':     $mdTabsCtrl.dynamicHeight ' +
              '}"> ' +
            '<div ' +
                'md-tabs-template="::tab.template" ' +
                'md-connected-if="tab.isActive()" ' +
                'md-scope="::tab.parent" ' +
                'ng-if="$mdTabsCtrl.enableDisconnect || tab.shouldRender()"></div> ' +
          '</md-tab-content> ' +
        '</md-tabs-content-wrapper>';
    },
    controller:       'MdTabsController',
    controllerAs:     '$mdTabsCtrl',
    bindToController: true
  };
}

})();
(function(){
"use strict";


MdTabsDummyWrapper.$inject = ["$mdUtil", "$window"];angular
  .module('material.components.tabs')
  .directive('mdTabsDummyWrapper', MdTabsDummyWrapper);

/**
 * @private
 *
 * @param $mdUtil
 * @param $window
 * @returns {{require: string, link: link}}
 * @constructor
 *
 * @ngInject
 */
function MdTabsDummyWrapper ($mdUtil, $window) {
  return {
    require: '^?mdTabs',
    link:    function link (scope, element, attr, ctrl) {
      if (!ctrl) return;

      var observer;
      var disconnect;

      var mutationCallback = function() {
        ctrl.updatePagination();
        ctrl.updateInkBarStyles();
      };

      if ('MutationObserver' in $window) {
        var config = {
          childList: true,
          subtree: true,
          // Per https://bugzilla.mozilla.org/show_bug.cgi?id=1138368, browsers will not fire
          // the childList mutation, once a <span> element's innerText changes.
          // The characterData of the <span> element will change.
          characterData: true
        };

        observer = new MutationObserver(mutationCallback);
        observer.observe(element[0], config);
        disconnect = observer.disconnect.bind(observer);
      } else {
        var debounced = $mdUtil.debounce(mutationCallback, 15, null, false);

        element.on('DOMSubtreeModified', debounced);
        disconnect = element.off.bind(element, 'DOMSubtreeModified', debounced);
      }

      // Disconnect the observer
      scope.$on('$destroy', function() {
        disconnect();
      });
    }
  };
}

})();
(function(){
"use strict";


MdTabsTemplate.$inject = ["$compile", "$mdUtil"];angular
    .module('material.components.tabs')
    .directive('mdTabsTemplate', MdTabsTemplate);

function MdTabsTemplate ($compile, $mdUtil) {
  return {
    restrict: 'A',
    link:     link,
    scope:    {
      template:     '=mdTabsTemplate',
      connected:    '=?mdConnectedIf',
      compileScope: '=mdScope'
    },
    require:  '^?mdTabs'
  };
  function link (scope, element, attr, ctrl) {
    if (!ctrl) return;

    var compileScope = ctrl.enableDisconnect ? scope.compileScope.$new() : scope.compileScope;

    element.html(scope.template);
    $compile(element.contents())(compileScope);

    return $mdUtil.nextTick(handleScope);

    function handleScope () {
      scope.$watch('connected', function (value) { value === false ? disconnect() : reconnect(); });
      scope.$on('$destroy', reconnect);
    }

    function disconnect () {
      if (ctrl.enableDisconnect) $mdUtil.disconnectScope(compileScope);
    }

    function reconnect () {
      if (ctrl.enableDisconnect) $mdUtil.reconnectScope(compileScope);
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.toast
 * @description
 * Toast and Snackbar component.
 */
MdToastDirective.$inject = ["$mdToast"];
MdToastProvider.$inject = ["$$interimElementProvider"];
angular.module('material.components.toast', [
  'material.core',
  'material.components.button'
])
  .directive('mdToast', MdToastDirective)
  .provider('$mdToast', MdToastProvider);

/* @ngInject */
function MdToastDirective($mdToast) {
  return {
    restrict: 'E',
    link: function postLink(scope, element) {
      element.addClass('_md');     // private md component indicator for styling

      // When navigation force destroys an interimElement, then
      // listen and $destroy() that interim instance...
      scope.$on('$destroy', function() {
        $mdToast.destroy();
      });
    }
  };
}

/**
 * @ngdoc service
 * @name $mdToast
 * @module material.components.toast
 *
 * @description
 * `$mdToast` is a service to build a toast notification on any position
 * on the screen with an optional duration, and provides a simple promise API.
 *
 * The toast will be always positioned at the `bottom`, when the screen size is
 * between `600px` and `959px` (`sm` breakpoint)
 *
 * ## Restrictions on custom toasts
 * - The toast's template must have an outer `<md-toast>` element.
 * - For a toast action, use element with class `md-action`.
 * - Add the class `md-capsule` for curved corners.
 *
 * ### Custom Presets
 * Developers are also able to create their own preset, which can be easily used without repeating
 * their options each time.
 *
 * <hljs lang="js">
 *   $mdToastProvider.addPreset('testPreset', {
 *     options: function() {
 *       return {
 *         template:
 *           '<md-toast>' +
 *             '<div class="md-toast-content">' +
 *               'This is a custom preset' +
 *             '</div>' +
 *           '</md-toast>',
 *         controllerAs: 'toast',
 *         bindToController: true
 *       };
 *     }
 *   });
 * </hljs>
 *
 * After you created your preset at config phase, you can easily access it.
 *
 * <hljs lang="js">
 *   $mdToast.show(
 *     $mdToast.testPreset()
 *   );
 * </hljs>
 *
 * ## Parent container notes
 *
 * The toast is positioned using absolute positioning relative to its first non-static parent
 * container. Thus, if the requested parent container uses static positioning, we will temporarily
 * set its positioning to `relative` while the toast is visible and reset it when the toast is
 * hidden.
 *
 * Because of this, it is usually best to ensure that the parent container has a fixed height and
 * prevents scrolling by setting the `overflow: hidden;` style. Since the position is based off of
 * the parent's height, the toast may be mispositioned if you allow the parent to scroll.
 *
 * You can, however, have a scrollable element inside of the container; just make sure the
 * container itself does not scroll.
 *
 * <hljs lang="html">
 * <div layout-fill id="toast-container">
 *   <md-content>
 *     I can have lots of content and scroll!
 *   </md-content>
 * </div>
 * </hljs>
 *
 * @usage
 * <hljs lang="html">
 * <div ng-controller="MyController">
 *   <md-button ng-click="openToast()">
 *     Open a Toast!
 *   </md-button>
 * </div>
 * </hljs>
 *
 * <hljs lang="js">
 * var app = angular.module('app', ['ngMaterial']);
 * app.controller('MyController', function($scope, $mdToast) {
 *   $scope.openToast = function($event) {
 *     $mdToast.show($mdToast.simple().textContent('Hello!'));
 *     // Could also do $mdToast.showSimple('Hello');
 *   };
 * });
 * </hljs>
 */

/**
 * @ngdoc method
 * @name $mdToast#showSimple
 *
 * @param {string} message The message to display inside the toast
 * @description
 * Convenience method which builds and shows a simple toast.
 *
 * @returns {promise} A promise that can be resolved with `$mdToast.hide()` or
 * rejected with `$mdToast.cancel()`.
 */

/**
 * @ngdoc method
 * @name $mdToast#simple
 *
 * @description
 * Builds a preconfigured toast.
 *
 * @returns {obj} a `$mdToastPreset` with the following chainable configuration methods.
 *
 * _**Note:** These configuration methods are provided in addition to the methods provided by
 * the `build()` and `show()` methods below._
 *
 * <table class="md-api-table methods">
 *    <thead>
 *      <tr>
 *        <th>Method</th>
 *        <th>Description</th>
 *      </tr>
 *    </thead>
 *    <tbody>
 *      <tr>
 *        <td>`.textContent(string)`</td>
 *        <td>Sets the toast content to the specified string</td>
 *      </tr>
 *      <tr>
 *        <td>`.action(string)`</td>
 *        <td>
 *          Adds an action button. <br/>
 *          If clicked, the promise (returned from `show()`) will resolve with the value `'ok'`;
 *          otherwise, it is resolved with `true` after a `hideDelay` timeout.
 *        </td>
 *      </tr>
 *      <tr>
 *        <td>`.actionKey(string)`</td>
 *        <td>
 *          Adds a hotkey for the action button to the page. <br/>
 *          If the `actionKey` and `Control` key are pressed, the toast's action will be triggered.
 *        </td>
 *      </tr>
 *      <tr>
 *        <td>`.actionHint(string)`</td>
 *        <td>
 *          Text that a screen reader will announce to let the user know how to activate the
 *          action. <br>
 *          If an `actionKey` is defined, this defaults to:
 *          'Press Control-"`actionKey`" to ' followed by the `action`.
 *        </td>
 *      </tr>
 *      <tr>
 *        <td>`.dismissHint(string)`</td>
 *        <td>
 *          Text that a screen reader will announce to let the user know how to dismiss the toast.
 *          <br>Defaults to: "Press Escape to dismiss."
 *        </td>
 *      </tr>
 *      <tr>
 *        <td>`.highlightAction(boolean)`</td>
 *        <td>
 *          Whether or not the action button will have an additional highlight class.<br/>
 *          By default the `accent` color will be applied to the action button.
 *        </td>
 *      </tr>
 *      <tr>
 *        <td>`.highlightClass(string)`</td>
 *        <td>
 *          If set, the given class will be applied to the highlighted action button.<br/>
 *          This allows you to specify the highlight color easily. Highlight classes are
 *          `md-primary`, `md-warn`, and `md-accent`
 *        </td>
 *      </tr>
 *      <tr>
 *        <td>`.capsule(boolean)`</td>
 *        <td>
 *          Whether or not to add the `md-capsule` class to the toast to provide rounded corners
 *        </td>
 *      </tr>
 *      <tr>
 *        <td>`.theme(string)`</td>
 *        <td>
 *          Sets the theme on the toast to the requested theme. Default is `$mdThemingProvider`'s
 *          default.
 *        </td>
 *      </tr>
 *      <tr>
 *        <td>`.toastClass(string)`</td>
 *        <td>Sets a class on the toast element</td>
 *      </tr>
 *    </tbody>
 * </table>
 */

/**
 * @ngdoc method
 * @name $mdToast#updateTextContent
 *
 * @description
 * Updates the content of an existing toast. Useful for updating things like counts, etc.
 */

/**
 * @ngdoc method
 * @name $mdToast#build
 *
 * @description
 * Creates a custom `$mdToastPreset` that you can configure.
 *
 * @returns {obj} a `$mdToastPreset` with the chainable configuration methods for shows' options
 *   (see below).
 */

/**
 * @ngdoc method
 * @name $mdToast#show
 *
 * @description Shows the toast.
 *
 * @param {object} optionsOrPreset Either provide an `$mdToastPreset` returned from `simple()`
 * and `build()`, or an options object with the following properties:
 *
 *   - `templateUrl` - `{string=}`: The url of an html template file that will
 *     be used as the content of the toast. Restrictions: the template must
 *     have an outer `md-toast` element.
 *   - `template` - `{string=}`: Same as templateUrl, except this is an actual
 *     template string.
 *   - `autoWrap` - `{boolean=}`: Whether or not to automatically wrap the template content with a
 *     `<div class="md-toast-content">` if one is not provided. Defaults to true. Can be disabled
 *     if you provide a custom toast directive.
 *   - `scope` - `{object=}`: the scope to link the template / controller to. If none is specified,
 *     it will create a new child scope. This scope will be destroyed when the toast is removed
 *     unless `preserveScope` is set to true.
 *   - `preserveScope` - `{boolean=}`: whether to preserve the scope when the element is removed.
 *     Default is false
 *   - `hideDelay` - `{number=}`: The number of milliseconds the toast should stay active before
 *     automatically closing. Set to `0` or `false` to have the toast stay open until closed
 *     manually via an action in the toast, a hotkey, or a swipe gesture. For accessibility, toasts
 *     should not automatically close when they contain an action.<br>
 *     Defaults to: `3000`.
 *   - `position` - `{string=}`: Sets the position of the toast. <br/>
 *     Available: any combination of `'bottom'`, `'left'`, `'top'`, `'right'`, `'end'`, and
 *     `'start'`. The properties `'end'` and `'start'` are dynamic and can be used for RTL support.
 *     <br/>
 *     Default combination: `'bottom left'`.
 *   - `toastClass` - `{string=}`: A class to set on the toast element.
 *   - `controller` - `{string=}`: The controller to associate with this toast.
 *     The controller will be injected the local `$mdToast.hide()`, which is a function
 *     used to hide the toast.
 *   - `locals` - `{object=}`: An object containing key/value pairs. The keys will
 *     be used as names of values to inject into the controller. For example,
 *     `locals: {three: 3}` would inject `three` into the controller with the value
 *     of 3.
 *   - `bindToController` - `{boolean=}`: bind the locals to the controller, instead of passing
 *     them in.
 *   - `resolve` - `{object=}`: Similar to locals, except it takes promises as values
 *     and the toast will not open until the promises resolve.
 *   - `controllerAs` - `{string=}`: An alias to assign the controller to on the scope.
 *   - `parent` - `{element=}`: The element to append the toast to. Defaults to appending
 *     to the root element of the application.
 *
 * @returns {promise} A promise that can be resolved with `$mdToast.hide()` or
 * rejected with `$mdToast.cancel()`. `$mdToast.hide()` will resolve either with the Boolean
 * value `true` or the value passed as an argument to `$mdToast.hide()`.
 * `$mdToast.cancel()` will resolve the promise with the Boolean value `false`.
 */

/**
 * @ngdoc method
 * @name $mdToast#hide
 *
 * @description
 * Hide an existing toast and resolve the promise returned from `$mdToast.show()`.
 *
 * @param {*=} response An argument for the resolved promise.
 *
 * @returns {promise} A promise that is called when the existing element is removed from the DOM.
 * The promise is resolved with either the Boolean value `true` or the value passed as the
 * argument to `$mdToast.hide()`.
 */

/**
 * @ngdoc method
 * @name $mdToast#cancel
 *
 * @description
 * `DEPRECATED` - The promise returned from opening a toast is used only to notify about the
 * closing of the toast. As such, there isn't any reason to also allow that promise to be rejected,
 * since it's not clear what the difference between resolve and reject would be.
 *
 * Hide the existing toast and reject the promise returned from
 * `$mdToast.show()`.
 *
 * @param {*=} response An argument for the rejected promise.
 *
 * @returns {promise} A promise that is called when the existing element is removed from the DOM
 * The promise is resolved with the Boolean value `false`.
 */

function MdToastProvider($$interimElementProvider) {
  // Differentiate promise resolves: hide timeout (value == true) and hide action clicks
  // (value == ok).
  MdToastController.$inject = ["$mdToast", "$scope", "$log"];
  toastDefaultOptions.$inject = ["$animate", "$mdToast", "$mdUtil", "$mdMedia", "$document", "$q"];
  var ACTION_RESOLVE = 'ok';

  var activeToastContent;
  var $mdToast = $$interimElementProvider('$mdToast')
    .setDefaults({
      methods: ['position', 'hideDelay', 'capsule', 'parent', 'position', 'toastClass'],
      options: toastDefaultOptions
    })
    .addPreset('simple', {
      argOption: 'textContent',
      methods: ['textContent', 'content', 'action', 'actionKey', 'actionHint', 'highlightAction',
                'highlightClass', 'theme', 'parent', 'dismissHint'],
      options: /* @ngInject */ ["$mdToast", "$mdTheming", function($mdToast, $mdTheming) {
        return {
          template:
            '<md-toast md-theme="{{ toast.theme }}" ng-class="{\'md-capsule\': toast.capsule}">' +
            '  <div class="md-toast-content" aria-live="polite" aria-relevant="all">' +
            '    <span class="md-toast-text">' +
            '      {{ toast.content }}' +
            '    </span>' +
            '    <span class="md-visually-hidden">{{ toast.dismissHint }}</span>' +
            '    <span class="md-visually-hidden" ng-if="toast.action && toast.actionKey">' +
            '      {{ toast.actionHint }}' +
            '    </span>' +
            '    <md-button class="md-action" ng-if="toast.action" ng-click="toast.resolve()" ' +
            '               ng-class="highlightClasses">' +
            '      {{ toast.action }}' +
            '    </md-button>' +
            '  </div>' +
            '</md-toast>',
          controller: MdToastController,
          theme: $mdTheming.defaultTheme(),
          controllerAs: 'toast',
          bindToController: true
        };
      }]
    })
    .addMethod('updateTextContent', updateTextContent)
    // updateContent is deprecated. Use updateTextContent instead.
    // TODO remove this in 1.2.
    .addMethod('updateContent', updateTextContent);

    function updateTextContent(newContent) {
      activeToastContent = newContent;
    }

    return $mdToast;

  /**
   * Controller for the Toast interim elements.
   * @ngInject
   */
  function MdToastController($mdToast, $scope, $log) {
    // For compatibility with AngularJS 1.6+, we should always use the $onInit hook in
    // interimElements. The $mdCompiler simulates the $onInit hook for all versions.
    this.$onInit = function() {
      var self = this;

      if (self.highlightAction) {
        $scope.highlightClasses = [
          'md-highlight',
          self.highlightClass
        ];
      }

      // If an action is defined and no actionKey is specified, then log a warning.
      if (self.action && !self.actionKey) {
        $log.warn('Toasts with actions should define an actionKey for accessibility.',
          'Details: https://material.angularjs.org/latest/api/service/$mdToast#mdtoast-simple');
      }

      if (self.actionKey && !self.actionHint) {
        self.actionHint = 'Press Control-"' + self.actionKey + '" to ';
      }

      if (!self.dismissHint) {
        self.dismissHint = 'Press Escape to dismiss.';
      }

      $scope.$watch(function() { return activeToastContent; }, function() {
        self.content = activeToastContent;
      });

      this.resolve = function() {
        $mdToast.hide(ACTION_RESOLVE);
      };
    };
  }

  /* @ngInject */
  function toastDefaultOptions($animate, $mdToast, $mdUtil, $mdMedia, $document, $q) {
    var SWIPE_EVENTS = '$md.swipeleft $md.swiperight $md.swipeup $md.swipedown';
    return {
      onShow: onShow,
      onRemove: onRemove,
      toastClass: '',
      position: 'bottom left',
      themable: true,
      hideDelay: 3000,
      autoWrap: true,
      transformTemplate: function(template, options) {
        var shouldAddWrapper = options.autoWrap && template && !/md-toast-content/g.test(template);

        if (shouldAddWrapper) {
          // Root element of template will be <md-toast>. We need to wrap all of its content inside
          // of <div class="md-toast-content">. All templates provided here should be static,
          // developer-controlled content (meaning we're not attempting to guard against XSS).
          var templateRoot = document.createElement('md-template');
          templateRoot.innerHTML = template;

          // Iterate through all root children, to detect possible md-toast directives.
          for (var i = 0; i < templateRoot.children.length; i++) {
            if (templateRoot.children[i].nodeName === 'MD-TOAST') {
              var wrapper = angular.element('<div class="md-toast-content">');

              // Wrap the children of the `md-toast` directive in jqLite, to be able to append
              // multiple nodes with the same execution.
              wrapper.append(angular.element(templateRoot.children[i].childNodes));

              // Append the new wrapped element to the `md-toast` directive.
              templateRoot.children[i].appendChild(wrapper[0]);
            }
          }

          // We have to return the innerHTMl, because we do not want to have the `md-template`
          // element to be the root element of our interimElement.
          return templateRoot.innerHTML;
        }

        return template || '';
      }
    };

    function onShow(scope, element, options) {
      // support deprecated #content method
      // TODO remove support for content in 1.2.
      activeToastContent = options.textContent || options.content;

      var isSmScreen = !$mdMedia('gt-sm');

      element = $mdUtil.extractElementByName(element, 'md-toast', true);
      options.element = element;

      options.onSwipe = function(ev, gesture) {
        // Add the relevant swipe class to the element so it can animate correctly
        var swipe = ev.type.replace('$md.','');
        var direction = swipe.replace('swipe', '');

        // If the swipe direction is down/up but the toast came from top/bottom don't fade away
        // Unless the screen is small, then the toast always on bottom
        if ((direction === 'down' && options.position.indexOf('top') !== -1 && !isSmScreen) ||
            (direction === 'up' && (options.position.indexOf('bottom') !== -1 || isSmScreen))) {
          return;
        }

        if ((direction === 'left' || direction === 'right') && isSmScreen) {
          return;
        }

        element.addClass('md-' + swipe);
        $mdUtil.nextTick($mdToast.cancel);
      };
      options.openClass = toastOpenClass(options.position);

      element.addClass(options.toastClass);

      // 'top left' -> 'md-top md-left'
      options.parent.addClass(options.openClass);

      // static is the default position
      if ($mdUtil.hasComputedStyle(options.parent, 'position', 'static')) {
        options.parent.css('position', 'relative');
      }

      setupActionKeyListener(scope.toast && scope.toast.actionKey ?
        scope.toast.actionKey : undefined);

      element.on(SWIPE_EVENTS, options.onSwipe);
      element.addClass(isSmScreen ? 'md-bottom' : options.position.split(' ').map(function(pos) {
        return 'md-' + pos;
      }).join(' '));

      if (options.parent) {
        options.parent.addClass('md-toast-animating');
      }
      return $animate.enter(element, options.parent).then(function() {
        if (options.parent) {
          options.parent.removeClass('md-toast-animating');
        }
      });
    }

    /**
     * @param {object} scope the toast's scope
     * @param {JQLite} element the toast to be removed
     * @param {object} options
     * @return {Promise<*>} a Promise to remove the element immediately or to animate it out.
     */
    function onRemove(scope, element, options) {
      if (scope.toast && scope.toast.actionKey) {
        removeActionKeyListener();
      }
      element.off(SWIPE_EVENTS, options.onSwipe);
      if (options.parent) options.parent.addClass('md-toast-animating');
      if (options.openClass) options.parent.removeClass(options.openClass);

      // Don't run the leave animation if the element has already been destroyed.
      return ((options.$destroy === true) ? $q.when(element.remove()) : $animate.leave(element))
        .then(function () {
          if (options.parent) options.parent.removeClass('md-toast-animating');
          if ($mdUtil.hasComputedStyle(options.parent, 'position', 'static')) {
            options.parent.css('position', '');
          }
        });
    }

    function toastOpenClass(position) {
      // For mobile, always open full-width on bottom
      if (!$mdMedia('gt-xs')) {
        return 'md-toast-open-bottom';
      }

      return 'md-toast-open-' + (position.indexOf('top') > -1 ? 'top' : 'bottom');
    }

    function setupActionKeyListener(actionKey) {
      /**
       * @param {KeyboardEvent} event
       */
      var handleKeyDown = function(event) {
        if (event.key === 'Escape') {
          $mdToast.hide(false);
        }
        if (actionKey && event.key === actionKey && event.ctrlKey) {
          $mdToast.hide(ACTION_RESOLVE);
        }
      };
      $document.on('keydown', handleKeyDown);
    }

    function removeActionKeyListener() {
      $document.off('keydown');
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.toolbar
 */
mdToolbarDirective.$inject = ["$$rAF", "$mdConstant", "$mdUtil", "$mdTheming", "$animate", "$timeout"];
angular.module('material.components.toolbar', [
  'material.core',
  'material.components.content'
])
  .directive('mdToolbar', mdToolbarDirective);

/**
 * @ngdoc directive
 * @name mdToolbar
 * @module material.components.toolbar
 * @restrict E
 * @description
 * `md-toolbar` is used to place a toolbar in your app.
 *
 * Toolbars are usually used above a content area to display the title of the
 * current page, and show relevant action buttons for that page.
 *
 * You can change the height of the toolbar by adding either the
 * `md-medium-tall` or `md-tall` class to the toolbar.
 *
 * @usage
 * <hljs lang="html">
 * <div layout="column" layout-fill>
 *   <md-toolbar>
 *
 *     <div class="md-toolbar-tools">
 *       <h2 md-truncate flex>My App's Title</h2>
 *
 *       <md-button>
 *         Right Bar Button
 *       </md-button>
 *     </div>
 *
 *   </md-toolbar>
 *   <md-content>
 *     Hello!
 *   </md-content>
 * </div>
 * </hljs>
 *
 * <i><b>Note:</b> The code above shows usage with the `md-truncate` component which provides an
 * ellipsis if the title is longer than the width of the Toolbar.</i>
 *
 * ## CSS & Styles
 *
 * The `<md-toolbar>` provides a few custom CSS classes that you may use to enhance the
 * functionality of your toolbar.
 *
 * <div>
 * <docs-css-api-table>
 *
 *   <docs-css-selector code="md-toolbar .md-toolbar-tools">
 *     The `md-toolbar-tools` class provides quite a bit of automatic styling for your toolbar
 *     buttons and text. When applied, it will center the buttons and text vertically for you.
 *   </docs-css-selector>
 *
 * </docs-css-api-table>
 * </div>
 *
 * ### Private Classes
 *
 * Currently, the only private class is the `md-toolbar-transitions` class. All other classes are
 * considered public.
 *
 * @param {boolean=} md-scroll-shrink Whether the header should shrink away as
 * the user scrolls down, and reveal itself as the user scrolls up.
 *
 * _**Note (1):** for scrollShrink to work, the toolbar must be a sibling of a
 * `md-content` element, placed before it. See the scroll shrink demo._
 *
 * _**Note (2):** The `md-scroll-shrink` attribute is only parsed on component
 * initialization, it does not watch for scope changes._
 *
 *
 * @param {number=} md-shrink-speed-factor How much to change the speed of the toolbar's
 * shrinking by. For example, if 0.25 is given then the toolbar will shrink
 * at one fourth the rate at which the user scrolls down. Default 0.5.
 *
 */

function mdToolbarDirective($$rAF, $mdConstant, $mdUtil, $mdTheming, $animate, $timeout) {
  var translateY = angular.bind(null, $mdUtil.supplant, 'translate3d(0,{0}px,0)');

  return {
    template: '',
    restrict: 'E',

    link: function(scope, element, attr) {

      element.addClass('_md');     // private md component indicator for styling
      $mdTheming(element);

      $mdUtil.nextTick(function () {
        element.addClass('_md-toolbar-transitions');     // adding toolbar transitions after digest
      }, false);

      if (angular.isDefined(attr.mdScrollShrink)) {
        setupScrollShrink();
      }

      function setupScrollShrink() {

        var toolbarHeight;
        var contentElement;
        var disableScrollShrink = angular.noop;

        // Current "y" position of scroll
        // Store the last scroll top position
        var y = 0;
        var prevScrollTop = 0;
        var shrinkSpeedFactor = attr.mdShrinkSpeedFactor || 0.5;

        var debouncedContentScroll = $$rAF.throttle(onContentScroll);
        var debouncedUpdateHeight = $mdUtil.debounce(updateToolbarHeight, 5 * 1000);

        // Wait for $mdContentLoaded event from mdContent directive.
        // If the mdContent element is a sibling of our toolbar, hook it up
        // to scroll events.

        scope.$on('$mdContentLoaded', onMdContentLoad);

        // If the toolbar is used inside an ng-if statement, we may miss the
        // $mdContentLoaded event, so we attempt to fake it if we have a
        // md-content close enough.

        attr.$observe('mdScrollShrink', onChangeScrollShrink);

        // If the toolbar has ngShow or ngHide we need to update height immediately as it changed
        // and not wait for $mdUtil.debounce to happen

        if (attr.ngShow) { scope.$watch(attr.ngShow, updateToolbarHeight); }
        if (attr.ngHide) { scope.$watch(attr.ngHide, updateToolbarHeight); }

        // If the scope is destroyed (which could happen with ng-if), make sure
        // to disable scroll shrinking again

        scope.$on('$destroy', disableScrollShrink);

        /**
         * @param {string} shrinkWithScroll value of md-scroll-shrink attribute
         */
        function onChangeScrollShrink(shrinkWithScroll) {
          var closestContent = $mdUtil.getSiblings(element, 'md-content');

          // If there are content elements, fake the call using the first content element.
          // This might still fail if the content element isn't a sibling of the toolbar.
          if (!contentElement && closestContent.length) {
            onMdContentLoad(null, closestContent[0]);
          }

          // Evaluate the expression
          shrinkWithScroll = scope.$eval(shrinkWithScroll);

          // Disable only if the attribute's expression evaluates to false
          if (shrinkWithScroll === false) {
            disableScrollShrink();
          } else {
            disableScrollShrink = enableScrollShrink();
          }
        }

        /**
         * @param {null} $event $mdContentLoaded always has a null event
         * @param {JQLite} newContentEl JQLite object containing an md-content
         */
        function onMdContentLoad($event, newContentEl) {
          // Toolbar and content must be siblings
          if (newContentEl && element.parent()[0] === newContentEl.parent()[0]) {
            // unhook old content event listener if exists
            if (contentElement) {
              contentElement.off('scroll', debouncedContentScroll);
            }

            contentElement = newContentEl;
            disableScrollShrink = enableScrollShrink();
          }
        }

        /**
         *
         */
        function onContentScroll(e) {
          var scrollTop = e ? e.target.scrollTop : prevScrollTop;

          debouncedUpdateHeight();

          y = Math.min(
            toolbarHeight / shrinkSpeedFactor,
            Math.max(0, y + scrollTop - prevScrollTop)
          );

          element.css($mdConstant.CSS.TRANSFORM, translateY([-y * shrinkSpeedFactor]));
          contentElement.css($mdConstant.CSS.TRANSFORM, translateY([(toolbarHeight - y) * shrinkSpeedFactor]));

          prevScrollTop = scrollTop;

          $mdUtil.nextTick(function() {
            var hasWhiteFrame = element.hasClass('md-whiteframe-z1');

            if (hasWhiteFrame && !y) {
              $animate.removeClass(element, 'md-whiteframe-z1');
            } else if (!hasWhiteFrame && y) {
              $animate.addClass(element, 'md-whiteframe-z1');
            }
          });

        }

        /**
         *
         */
        function enableScrollShrink() {
          if (!contentElement)     return angular.noop;           // no md-content

          contentElement.on('scroll', debouncedContentScroll);
          contentElement.attr('scroll-shrink', 'true');

          $timeout(updateToolbarHeight);

          return function disableScrollShrink() {
            contentElement.off('scroll', debouncedContentScroll);
            contentElement.attr('scroll-shrink', 'false');

            updateToolbarHeight();
          };
        }

        /**
         *
         */
        function updateToolbarHeight() {
          toolbarHeight = element.prop('offsetHeight');
          // Add a negative margin-top the size of the toolbar to the content el.
          // The content will start transformed down the toolbarHeight amount,
          // so everything looks normal.
          //
          // As the user scrolls down, the content will be transformed up slowly
          // to put the content underneath where the toolbar was.
          var margin = (-toolbarHeight * shrinkSpeedFactor) + 'px';

          contentElement.css({
            "margin-top": margin,
            "margin-bottom": margin
          });

          onContentScroll();
        }

      }

    }
  };

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.tooltip
 */
MdTooltipDirective.$inject = ["$timeout", "$window", "$$rAF", "$document", "$interpolate", "$mdUtil", "$mdPanel", "$$mdTooltipRegistry"];
angular
    .module('material.components.tooltip', [
      'material.core',
      'material.components.panel'
    ])
    .directive('mdTooltip', MdTooltipDirective)
    .service('$$mdTooltipRegistry', MdTooltipRegistry);


/**
 * @ngdoc directive
 * @name mdTooltip
 * @module material.components.tooltip
 * @description
 * Tooltips are used to describe elements that are interactive and primarily
 * graphical (not textual).
 *
 * Place a `<md-tooltip>` as a child of the element it describes.
 *
 * A tooltip will activate when the user hovers over, focuses, or touches the
 * parent element.
 *
 * @usage
 * <hljs lang="html">
 *   <md-button class="md-fab md-accent" aria-label="Play">
 *     <md-tooltip>Play Music</md-tooltip>
 *     <md-icon md-svg-src="img/icons/ic_play_arrow_24px.svg"></md-icon>
 *   </md-button>
 * </hljs>
 *
 * @param {number=} md-z-index The visual level that the tooltip will appear
 *     in comparison with the rest of the elements of the application.
 * @param {expression=} md-visible Boolean bound to whether the tooltip is
 *     currently visible.
 * @param {number=} md-delay How many milliseconds to wait to show the tooltip
 *     after the user hovers over, focuses, or touches the parent element.
 *     Defaults to 0ms on non-touch devices and 75ms on touch.
 * @param {boolean=} md-autohide If present or provided with a boolean value,
 *     the tooltip will hide on mouse leave, regardless of focus.
 * @param {string=} md-direction The direction that the tooltip is shown,
 *     relative to the parent element. Supports top, right, bottom, and left.
 *     Defaults to bottom.
 */
function MdTooltipDirective($timeout, $window, $$rAF, $document, $interpolate,
    $mdUtil, $mdPanel, $$mdTooltipRegistry) {

  var ENTER_EVENTS = 'focus touchstart mouseenter';
  var LEAVE_EVENTS = 'blur touchcancel mouseleave';
  var TOOLTIP_DEFAULT_Z_INDEX = 100;
  var TOOLTIP_DEFAULT_SHOW_DELAY = 0;
  var TOOLTIP_DEFAULT_DIRECTION = 'bottom';
  var TOOLTIP_DIRECTIONS = {
    top: { x: $mdPanel.xPosition.CENTER, y: $mdPanel.yPosition.ABOVE },
    right: { x: $mdPanel.xPosition.OFFSET_END, y: $mdPanel.yPosition.CENTER },
    bottom: { x: $mdPanel.xPosition.CENTER, y: $mdPanel.yPosition.BELOW },
    left: { x: $mdPanel.xPosition.OFFSET_START, y: $mdPanel.yPosition.CENTER }
  };

  return {
    restrict: 'E',
    priority: 210, // Before ngAria
    scope: {
      mdZIndex: '=?mdZIndex',
      mdDelay: '=?mdDelay',
      mdVisible: '=?mdVisible',
      mdAutohide: '=?mdAutohide',
      mdDirection: '@?mdDirection' // Do not expect expressions.
    },
    link: linkFunc
  };

  function linkFunc(scope, element, attr) {
    // Set constants.
    var tooltipId = 'md-tooltip-' + $mdUtil.nextUid();
    var parent = $mdUtil.getParentWithPointerEvents(element);
    var debouncedOnResize = $$rAF.throttle(updatePosition);
    var mouseActive = false;
    var origin, position, panelPosition, panelRef, autohide, showTimeout,
        elementFocusedOnWindowBlur = null;

    // Set defaults
    setDefaults();

    // Set parent aria-label.
    addAriaLabel();

    // Remove the element from its current DOM position.
    element.detach();

    updatePosition();
    bindEvents();
    configureWatchers();

    function setDefaults() {
      scope.mdZIndex = scope.mdZIndex || TOOLTIP_DEFAULT_Z_INDEX;
      scope.mdDelay = scope.mdDelay || TOOLTIP_DEFAULT_SHOW_DELAY;
      if (!TOOLTIP_DIRECTIONS[scope.mdDirection]) {
        scope.mdDirection = TOOLTIP_DEFAULT_DIRECTION;
      }
    }

    function addAriaLabel(labelText) {
      // Only interpolate the text from the HTML element because otherwise the custom text could
      // be interpolated twice and cause XSS violations.
      var interpolatedText = labelText || $interpolate(element.text().trim())(scope.$parent);

      // Only add the `aria-label` to the parent if there isn't already one, if there isn't an
      // already present `aria-labelledby`, or if the previous `aria-label` was added by the
      // tooltip directive.
      if (
        (!parent.attr('aria-label') && !parent.attr('aria-labelledby')) ||
        parent.attr('md-labeled-by-tooltip')
      ) {
        parent.attr('aria-label', interpolatedText);

        // Set the `md-labeled-by-tooltip` attribute if it has not already been set.
        if (!parent.attr('md-labeled-by-tooltip')) {
          parent.attr('md-labeled-by-tooltip', tooltipId);
        }
      }
    }

    function updatePosition() {
      setDefaults();

      // If the panel has already been created, remove the current origin
      // class from the panel element.
      if (panelRef && panelRef.panelEl) {
        panelRef.panelEl.removeClass(origin);
      }

      // Set the panel element origin class based off of the current
      // mdDirection.
      origin = 'md-origin-' + scope.mdDirection;

      // Create the position of the panel based off of the mdDirection.
      position = TOOLTIP_DIRECTIONS[scope.mdDirection];

      // Using the newly created position object, use the MdPanel
      // panelPosition API to build the panel's position.
      panelPosition = $mdPanel.newPanelPosition()
          .relativeTo(parent)
          .addPanelPosition(position.x, position.y);

      // If the panel has already been created, add the new origin class to
      // the panel element and update it's position with the panelPosition.
      if (panelRef && panelRef.panelEl) {
        panelRef.panelEl.addClass(origin);
        panelRef.updatePosition(panelPosition);
      }
    }

    function bindEvents() {
      // Add a mutationObserver where there is support for it and the need
      // for it in the form of viable host(parent[0]).
      if (parent[0] && 'MutationObserver' in $window) {
        // Use a mutationObserver to tackle #2602.
        var attributeObserver = new MutationObserver(function(mutations) {
          if (isDisabledMutation(mutations)) {
            $mdUtil.nextTick(function() {
              setVisible(false);
            });
          }
        });

        attributeObserver.observe(parent[0], {
          attributes: true
        });
      }

      elementFocusedOnWindowBlur = false;

      $$mdTooltipRegistry.register('scroll', windowScrollEventHandler, true);
      $$mdTooltipRegistry.register('blur', windowBlurEventHandler);
      $$mdTooltipRegistry.register('resize', debouncedOnResize);

      scope.$on('$destroy', onDestroy);

      // To avoid 'synthetic clicks', we listen to mousedown instead of
      // 'click'.
      parent.on('mousedown', mousedownEventHandler);
      parent.on(ENTER_EVENTS, enterEventHandler);

      function isDisabledMutation(mutations) {
        mutations.some(function(mutation) {
          return mutation.attributeName === 'disabled' && parent[0].disabled;
        });
        return false;
      }

      function windowScrollEventHandler() {
        setVisible(false);
      }

      function windowBlurEventHandler() {
        elementFocusedOnWindowBlur = document.activeElement === parent[0];
      }

      function enterEventHandler($event) {
        // Prevent the tooltip from showing when the window is receiving
        // focus.
        if ($event.type === 'focus' && elementFocusedOnWindowBlur) {
          elementFocusedOnWindowBlur = false;
        } else if (!scope.mdVisible) {
          parent.on(LEAVE_EVENTS, leaveEventHandler);
          setVisible(true);

          // If the user is on a touch device, we should bind the tap away
          // after the 'touched' in order to prevent the tooltip being
          // removed immediately.
          if ($event.type === 'touchstart') {
            parent.one('touchend', function() {
              $mdUtil.nextTick(function() {
                $document.one('touchend', leaveEventHandler);
              }, false);
            });
          }
        }
      }

      function leaveEventHandler() {
        autohide = scope.hasOwnProperty('mdAutohide') ?
            scope.mdAutohide :
            attr.hasOwnProperty('mdAutohide');

        if (autohide || mouseActive ||
            $document[0].activeElement !== parent[0]) {
          // When a show timeout is currently in progress, then we have
          // to cancel it, otherwise the tooltip will remain showing
          // without focus or hover.
          if (showTimeout) {
            $timeout.cancel(showTimeout);
            setVisible.queued = false;
            showTimeout = null;
          }

          parent.off(LEAVE_EVENTS, leaveEventHandler);
          parent.triggerHandler('blur');
          setVisible(false);
        }
        mouseActive = false;
      }

      function mousedownEventHandler() {
        mouseActive = true;
      }

      function onDestroy() {
        $$mdTooltipRegistry.deregister('scroll', windowScrollEventHandler, true);
        $$mdTooltipRegistry.deregister('blur', windowBlurEventHandler);
        $$mdTooltipRegistry.deregister('resize', debouncedOnResize);

        parent
            .off(ENTER_EVENTS, enterEventHandler)
            .off(LEAVE_EVENTS, leaveEventHandler)
            .off('mousedown', mousedownEventHandler);

        // Trigger the handler in case any of the tooltips are
        // still visible.
        leaveEventHandler();
        attributeObserver && attributeObserver.disconnect();
      }
    }

    function configureWatchers() {
      if (element[0] && 'MutationObserver' in $window) {
        var attributeObserver = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
            if (mutation.attributeName === 'md-visible' &&
                !scope.visibleWatcher) {
              scope.visibleWatcher = scope.$watch('mdVisible',
                  onVisibleChanged);
            }
          });
        });

        attributeObserver.observe(element[0], {
          attributes: true
        });

        // Build watcher only if mdVisible is being used.
        if (attr.hasOwnProperty('mdVisible')) {
          scope.visibleWatcher = scope.$watch('mdVisible',
              onVisibleChanged);
        }
      } else {
        // MutationObserver not supported
        scope.visibleWatcher = scope.$watch('mdVisible', onVisibleChanged);
      }

      // Direction watcher
      scope.$watch('mdDirection', updatePosition);

      // Clean up if the element or parent was removed via jqLite's .remove.
      // A couple of notes:
      //   - In these cases the scope might not have been destroyed, which
      //     is why we destroy it manually. An example of this can be having
      //     `md-visible="false"` and adding tooltips while they're
      //     invisible. If `md-visible` becomes true, at some point, you'd
      //     usually get a lot of tooltips.
      //   - We use `.one`, not `.on`, because this only needs to fire once.
      //     If we were using `.on`, it would get thrown into an infinite
      //     loop.
      //   - This kicks off the scope's `$destroy` event which finishes the
      //     cleanup.
      element.one('$destroy', onElementDestroy);
      parent.one('$destroy', onElementDestroy);
      scope.$on('$destroy', function() {
        setVisible(false);
        panelRef && panelRef.destroy();
        attributeObserver && attributeObserver.disconnect();
        element.remove();
      });

      // Updates the aria-label when the element text changes. This watch
      // doesn't need to be set up if the element doesn't have any data
      // bindings.
      if (element.text().indexOf($interpolate.startSymbol()) > -1) {
        scope.$watch(function() {
          return element.text().trim();
        }, addAriaLabel);
      }

      function onElementDestroy() {
        scope.$destroy();
      }
    }

    function setVisible(value) {
      // Break if passed value is already in queue or there is no queue and
      // passed value is current in the controller.
      if (setVisible.queued && setVisible.value === !!value ||
          !setVisible.queued && scope.mdVisible === !!value) {
        return;
      }
      setVisible.value = !!value;

      if (!setVisible.queued) {
        if (value) {
          setVisible.queued = true;
          showTimeout = $timeout(function() {
            scope.mdVisible = setVisible.value;
            setVisible.queued = false;
            showTimeout = null;
            if (!scope.visibleWatcher) {
              onVisibleChanged(scope.mdVisible);
            }
          }, scope.mdDelay);
        } else {
          $mdUtil.nextTick(function() {
            scope.mdVisible = false;
            if (!scope.visibleWatcher) {
              onVisibleChanged(false);
            }
          });
        }
      }
    }

    function onVisibleChanged(isVisible) {
      isVisible ? showTooltip() : hideTooltip();
    }

    function showTooltip() {
      // Do not show the tooltip if the text is empty.
      if (!element[0].textContent.trim()) {
        throw new Error('Text for the tooltip has not been provided. ' +
            'Please include text within the mdTooltip element.');
      }

      if (!panelRef) {
        var attachTo = angular.element(document.body);
        var panelAnimation = $mdPanel.newPanelAnimation()
            .openFrom(parent)
            .closeTo(parent)
            .withAnimation({
              open: 'md-show',
              close: 'md-hide'
            });

        var panelConfig = {
          id: tooltipId,
          attachTo: attachTo,
          contentElement: element,
          propagateContainerEvents: true,
          panelClass: 'md-tooltip',
          animation: panelAnimation,
          position: panelPosition,
          zIndex: scope.mdZIndex,
          focusOnOpen: false,
          onDomAdded: function() {
            panelRef.panelEl.addClass(origin);
          }
        };

        panelRef = $mdPanel.create(panelConfig);
      }

      panelRef.open().then(function() {
        panelRef.panelEl.attr('role', 'tooltip');
      });
    }

    function hideTooltip() {
      panelRef && panelRef.close();
    }
  }

}


/**
 * Service that is used to reduce the amount of listeners that are being
 * registered on the `window` by the tooltip component. Works by collecting
 * the individual event handlers and dispatching them from a global handler.
 *
 * @ngInject
 */
function MdTooltipRegistry() {
  var listeners = {};
  var ngWindow = angular.element(window);

  return {
    register: register,
    deregister: deregister
  };

  /**
   * Global event handler that dispatches the registered handlers in the
   * service.
   * @param {!Event} event Event object passed in by the browser
   */
  function globalEventHandler(event) {
    if (listeners[event.type]) {
      listeners[event.type].forEach(function(currentHandler) {
        currentHandler.call(this, event);
      }, this);
    }
  }

  /**
   * Registers a new handler with the service.
   * @param {string} type Type of event to be registered.
   * @param {!Function} handler Event handler.
   * @param {boolean} useCapture Whether to use event capturing.
   */
  function register(type, handler, useCapture) {
    var handlers = listeners[type] = listeners[type] || [];

    if (!handlers.length) {
      useCapture ? window.addEventListener(type, globalEventHandler, true) :
          ngWindow.on(type, globalEventHandler);
    }

    if (handlers.indexOf(handler) === -1) {
      handlers.push(handler);
    }
  }

  /**
   * Removes an event handler from the service.
   * @param {string} type Type of event handler.
   * @param {!Function} handler The event handler itself.
   * @param {boolean} useCapture Whether the event handler used event capturing.
   */
  function deregister(type, handler, useCapture) {
    var handlers = listeners[type];
    var index = handlers ? handlers.indexOf(handler) : -1;

    if (index > -1) {
      handlers.splice(index, 1);

      if (handlers.length === 0) {
        useCapture ? window.removeEventListener(type, globalEventHandler, true) :
            ngWindow.off(type, globalEventHandler);
      }
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.truncate
 */
MdTruncateController.$inject = ["$element"];
angular.module('material.components.truncate', ['material.core'])
  .directive('mdTruncate', MdTruncateDirective);

/**
 * @ngdoc directive
 * @name mdTruncate
 * @module material.components.truncate
 * @restrict AE
 * @description
 *
 * The `md-truncate` component displays a label that will automatically clip text which is wider
 * than the component. By default, it displays an ellipsis, but you may apply the `md-clip` CSS
 * class to override this default and use a standard "clipping" approach.
 *
 * <i><b>Note:</b> The `md-truncate` component does not automatically adjust it's width. You must
 * provide the `flex` attribute, or some other CSS-based width management. See the
 * <a ng-href="./demo/truncate">demos</a> for examples.</i>
 *
 * @usage
 *
 * ### As an Element
 *
 * <hljs lang="html">
 *   <div layout="row">
 *     <md-button>Back</md-button>
 *
 *     <md-truncate flex>Chapter 1 - The Way of the Old West</md-truncate>
 *
 *     <md-button>Forward</md-button>
 *   </div>
 * </hljs>
 *
 * ### As an Attribute
 *
 * <hljs lang="html">
 *   <h2 md-truncate style="max-width: 100px;">Some Title With a Lot of Text</h2>
 * </hljs>
 *
 * ## CSS & Styles
 *
 * `<md-truncate>` provides two CSS classes that you may use to control the type of clipping.
 *
 * <i><b>Note:</b> The `md-truncate` also applies a setting of `width: 0;` when used with the `flex`
 * attribute to fix an issue with the flex element not shrinking properly.</i>
 *
 * <div>
 * <docs-css-api-table>
 *
 *   <docs-css-selector code=".md-ellipsis">
 *     Assigns the "ellipsis" behavior (default) which will cut off mid-word and append an ellipsis
 *     (&hellip;) to the end of the text.
 *   </docs-css-selector>
 *
 *   <docs-css-selector code=".md-clip">
 *     Assigns the "clipping" behavior which will simply chop off the text. This may happen
 *     mid-word or even mid-character.
 *   </docs-css-selector>
 *
 * </docs-css-api-table>
 * </div>
 */
function MdTruncateDirective() {
  return {
    restrict: 'AE',

    controller: MdTruncateController
  };
}

/**
 * Controller for the <md-truncate> component.
 *
 * @param $element The md-truncate element.
 *
 * @constructor
 * @ngInject
 */
function MdTruncateController($element) {
  $element.addClass('md-truncate');
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.virtualRepeat
 */
VirtualRepeatContainerController.$inject = ["$$rAF", "$mdUtil", "$mdConstant", "$parse", "$rootScope", "$window", "$scope", "$element", "$attrs"];
VirtualRepeatController.$inject = ["$scope", "$element", "$attrs", "$browser", "$document", "$rootScope", "$$rAF", "$mdUtil"];
VirtualRepeatDirective.$inject = ["$parse"];
angular.module('material.components.virtualRepeat', [
  'material.core',
  'material.components.showHide'
])
.directive('mdVirtualRepeatContainer', VirtualRepeatContainerDirective)
.directive('mdVirtualRepeat', VirtualRepeatDirective)
.directive('mdForceHeight', ForceHeightDirective);


/**
 * @ngdoc directive
 * @name mdVirtualRepeatContainer
 * @module material.components.virtualRepeat
 * @restrict E
 * @description
 * `md-virtual-repeat-container` provides the scroll container for
 * <a ng-href="api/directive/mdVirtualRepeat">md-virtual-repeat</a>.
 *
 * VirtualRepeat is a limited substitute for `ng-repeat` that renders only
 * enough DOM nodes to fill the container, recycling them as the user scrolls.
 *
 * Once an element is not visible anymore, the Virtual Repeat recycles the element and reuses it
 * for another visible item by replacing the previous data set with the set of currently visible
 * elements.
 *
 * ### Common Issues
 *
 * - When having one-time bindings inside of the view template, the Virtual Repeat will not properly
 *   update the bindings for new items, since the view will be recycled.
 * - Directives inside of a Virtual Repeat will be only compiled (linked) once, because those
 *   items will be recycled and used for other items.
 *   The Virtual Repeat just updates the scope bindings.
 *
 *
 * ### Notes
 *
 * > The VirtualRepeat is a similar implementation to the Android
 * [RecyclerView](https://developer.android.com/reference/android/support/v7/widget/RecyclerView.html).
 *
 * <!-- This comment forces a break between blockquotes //-->
 *
 * > Please also review the <a ng-href="api/directive/mdVirtualRepeat">mdVirtualRepeat</a>
 * documentation for more information.
 *
 *
 * @usage
 * <hljs lang="html">
 *
 * <md-virtual-repeat-container md-top-index="topIndex">
 *   <div md-virtual-repeat="i in items" md-item-size="20">Hello {{i}}!</div>
 * </md-virtual-repeat-container>
 * </hljs>
 *
 * @param {boolean=} md-auto-shrink When present and the container will shrink to fit
 *     the number of items in the `md-virtual-repeat`.
 * @param {number=} md-auto-shrink-min Minimum number of items that md-auto-shrink
 *     will shrink to. Default: `0`.
 * @param {boolean=} md-orient-horizontal Whether the container should scroll horizontally.
 *     The default is `false` which indicates vertical orientation and scrolling.
 * @param {number=} md-top-index Binds the index of the item that is at the top of the scroll
 *     container to `$scope`. It can both read and set the scroll position.
 */
function VirtualRepeatContainerDirective() {
  return {
    controller: VirtualRepeatContainerController,
    template: virtualRepeatContainerTemplate,
    compile: function virtualRepeatContainerCompile($element, $attrs) {
      $element
          .addClass('md-virtual-repeat-container')
          .addClass($attrs.hasOwnProperty('mdOrientHorizontal')
              ? 'md-orient-horizontal'
              : 'md-orient-vertical');
    }
  };
}


function virtualRepeatContainerTemplate($element) {
  return '<div class="md-virtual-repeat-scroller" role="presentation">' +
    '<div class="md-virtual-repeat-sizer" role="presentation"></div>' +
    '<div class="md-virtual-repeat-offsetter" role="presentation">' +
      $element[0].innerHTML +
    '</div></div>';
}

/**
 * Number of additional elements to render above and below the visible area inside
 * of the virtual repeat container. A higher number results in less flicker when scrolling
 * very quickly in Safari, but comes with a higher rendering and dirty-checking cost.
 * @const {number}
 */
var NUM_EXTRA = 3;

/** @ngInject */
function VirtualRepeatContainerController($$rAF, $mdUtil, $mdConstant, $parse, $rootScope, $window,
                                          $scope, $element, $attrs) {
  this.$rootScope = $rootScope;
  this.$scope = $scope;
  this.$element = $element;
  this.$attrs = $attrs;

  /** @type {number} The width or height of the container */
  this.size = 0;
  /** @type {number} The scroll width or height of the scroller */
  this.scrollSize = 0;
  /** @type {number} The scrollLeft or scrollTop of the scroller */
  this.scrollOffset = 0;
  /** @type {boolean} Whether the scroller is oriented horizontally */
  this.horizontal = this.$attrs.hasOwnProperty('mdOrientHorizontal');
  /** @type {!VirtualRepeatController} The repeater inside of this container */
  this.repeater = null;
  /** @type {boolean} Whether auto-shrink is enabled */
  this.autoShrink = this.$attrs.hasOwnProperty('mdAutoShrink');
  /** @type {number} Minimum number of items to auto-shrink to */
  this.autoShrinkMin = parseInt(this.$attrs.mdAutoShrinkMin, 10) || 0;
  /** @type {?number} Original container size when shrank */
  this.originalSize = null;
  /** @type {number} Amount to offset the total scroll size by. */
  this.offsetSize = parseInt(this.$attrs.mdOffsetSize, 10) || 0;
  /** @type {?string} height or width element style on the container prior to auto-shrinking. */
  this.oldElementSize = null;
  /** @type {!number} Maximum amount of pixels allowed for a single DOM element */
  this.maxElementPixels = $mdConstant.ELEMENT_MAX_PIXELS;
  /** @type {string}  Direction of the text */
  this.ltr = !$mdUtil.isRtl(this.$attrs);

  if (this.$attrs.mdTopIndex) {
    /** @type {function(angular.Scope): number} Binds to topIndex on AngularJS scope */
    this.bindTopIndex = $parse(this.$attrs.mdTopIndex);
    /** @type {number} The index of the item that is at the top of the scroll container */
    this.topIndex = this.bindTopIndex(this.$scope);

    if (!angular.isDefined(this.topIndex)) {
      this.topIndex = 0;
      this.bindTopIndex.assign(this.$scope, 0);
    }

    this.$scope.$watch(this.bindTopIndex, angular.bind(this, function(newIndex) {
      if (newIndex !== this.topIndex) {
        this.scrollToIndex(newIndex);
      }
    }));
  } else {
    this.topIndex = 0;
  }

  this.scroller = $element[0].querySelector('.md-virtual-repeat-scroller');
  this.sizer = this.scroller.querySelector('.md-virtual-repeat-sizer');
  this.offsetter = this.scroller.querySelector('.md-virtual-repeat-offsetter');

  // After the DOM stabilizes, measure the initial size of the container and
  // make a best effort at re-measuring as it changes.
  var boundUpdateSize = angular.bind(this, this.updateSize);

  $$rAF(angular.bind(this, function() {
    boundUpdateSize();

    var debouncedUpdateSize = $mdUtil.debounce(boundUpdateSize, 10, null, false);
    var jWindow = angular.element($window);

    // Make one more attempt to get the size if it is 0.
    // This is not by any means a perfect approach, but there's really no
    // silver bullet here.
    if (!this.size) {
      debouncedUpdateSize();
    }

    jWindow.on('resize', debouncedUpdateSize);
    $scope.$on('$destroy', function() {
      jWindow.off('resize', debouncedUpdateSize);
    });

    $scope.$emit('$md-resize-enable');
    $scope.$on('$md-resize', boundUpdateSize);
  }));
}


/** Called by the md-virtual-repeat inside of the container at startup. */
VirtualRepeatContainerController.prototype.register = function(repeaterCtrl) {
  this.repeater = repeaterCtrl;

  angular.element(this.scroller)
      .on('scroll wheel touchmove touchend', angular.bind(this, this.handleScroll_));
};


/** @return {boolean} Whether the container is configured for horizontal scrolling. */
VirtualRepeatContainerController.prototype.isHorizontal = function() {
  return this.horizontal;
};


/** @return {number} The size (width or height) of the container. */
VirtualRepeatContainerController.prototype.getSize = function() {
  return this.size;
};


/**
 * Resizes the container.
 * @private
 * @param {number} size The new size to set.
 */
VirtualRepeatContainerController.prototype.setSize_ = function(size) {
  var dimension = this.getDimensionName_();

  this.size = size;
  this.$element[0].style[dimension] = size + 'px';
};


VirtualRepeatContainerController.prototype.unsetSize_ = function() {
  this.$element[0].style[this.getDimensionName_()] = this.oldElementSize;
  this.oldElementSize = null;
};


/** Instructs the container to re-measure its size. */
VirtualRepeatContainerController.prototype.updateSize = function() {
  // If the original size is already determined, we can skip the update.
  if (this.originalSize) return;

  var size = this.isHorizontal()
      ? this.$element[0].clientWidth
      : this.$element[0].clientHeight;

  if (size) {
    this.size = size;
  }

  // Recheck the scroll position after updating the size. This resolves
  // problems that can result if the scroll position was measured while the
  // element was display: none or detached from the document.
  this.handleScroll_();

  this.repeater && this.repeater.containerUpdated();
};


/** @return {number} The container's scrollHeight or scrollWidth. */
VirtualRepeatContainerController.prototype.getScrollSize = function() {
  return this.scrollSize;
};

/**
 * @returns {string} either width or height dimension
 * @private
 */
VirtualRepeatContainerController.prototype.getDimensionName_ = function() {
  return this.isHorizontal() ? 'width' : 'height';
};


/**
 * Sets the scroller element to the specified size.
 * @private
 * @param {number} size The new size.
 */
VirtualRepeatContainerController.prototype.sizeScroller_ = function(size) {
  var dimension =  this.getDimensionName_();
  var crossDimension = this.isHorizontal() ? 'height' : 'width';

  // Clear any existing dimensions.
  this.sizer.innerHTML = '';

  // If the size falls within the browser's maximum explicit size for a single element, we can
  // set the size and be done. Otherwise, we have to create children that add up the the desired
  // size.
  if (size < this.maxElementPixels) {
    this.sizer.style[dimension] = size + 'px';
  } else {
    this.sizer.style[dimension] = 'auto';
    this.sizer.style[crossDimension] = 'auto';

    // Divide the total size we have to render into N max-size pieces.
    var numChildren = Math.floor(size / this.maxElementPixels);

    // Element template to clone for each max-size piece.
    var sizerChild = document.createElement('div');
    sizerChild.style[dimension] = this.maxElementPixels + 'px';
    sizerChild.style[crossDimension] = '1px';

    for (var i = 0; i < numChildren; i++) {
      this.sizer.appendChild(sizerChild.cloneNode(false));
    }

    // Re-use the element template for the remainder.
    sizerChild.style[dimension] = (size - (numChildren * this.maxElementPixels)) + 'px';
    this.sizer.appendChild(sizerChild);
  }
};


/**
 * If auto-shrinking is enabled, shrinks or unshrinks as appropriate.
 * @private
 * @param {number} size The new size.
 */
VirtualRepeatContainerController.prototype.autoShrink_ = function(size) {
  var shrinkSize = Math.max(size, this.autoShrinkMin * this.repeater.getItemSize());

  if (this.autoShrink && shrinkSize !== this.size) {
    if (this.oldElementSize === null) {
      this.oldElementSize = this.$element[0].style[this.getDimensionName_()];
    }

    var currentSize = this.originalSize || this.size;

    if (!currentSize || shrinkSize < currentSize) {
      if (!this.originalSize) {
        this.originalSize = this.size;
      }

      // Now we update the containers size, because shrinking is enabled.
      this.setSize_(shrinkSize);
    } else if (this.originalSize !== null) {
      // Set the size back to our initial size.
      this.unsetSize_();

      var _originalSize = this.originalSize;
      this.originalSize = null;

      // We determine the repeaters size again, if the original size was zero.
      // The originalSize needs to be null, to be able to determine the size.
      if (!_originalSize) this.updateSize();

      // Apply the original size or the determined size back to the container, because
      // it has been overwritten before, in the shrink block.
      this.setSize_(_originalSize || this.size);
    }

    this.repeater.containerUpdated();
  }
};


/**
 * Sets the scrollHeight or scrollWidth. Called by the repeater based on
 * its item count and item size.
 * @param {number} itemsSize The total size of the items.
 */
VirtualRepeatContainerController.prototype.setScrollSize = function(itemsSize) {
  var size = itemsSize + this.offsetSize;
  if (this.scrollSize === size) return;

  this.sizeScroller_(size);
  this.autoShrink_(size);
  this.scrollSize = size;
};


/** @return {number} The container's current scroll offset. */
VirtualRepeatContainerController.prototype.getScrollOffset = function() {
  return this.scrollOffset;
};

/**
 * Scrolls to a given scrollTop position.
 * @param {number} position
 */
VirtualRepeatContainerController.prototype.scrollTo = function(position) {
  this.scroller[this.isHorizontal() ? 'scrollLeft' : 'scrollTop'] = position;
  this.handleScroll_();
};

/**
 * Scrolls the item with the given index to the top of the scroll container.
 * @param {number} index
 */
VirtualRepeatContainerController.prototype.scrollToIndex = function(index) {
  var itemSize = this.repeater.getItemSize();
  var itemsLength = this.repeater.itemsLength;
  if (index > itemsLength) {
    index = itemsLength - 1;
  }
  this.scrollTo(itemSize * index);
};

VirtualRepeatContainerController.prototype.resetScroll = function() {
  this.scrollTo(0);
};


VirtualRepeatContainerController.prototype.handleScroll_ = function() {
  if (!this.ltr && !this.maxSize) {
    this.scroller.scrollLeft = this.scrollSize;
    this.maxSize = this.scroller.scrollLeft;
  }
  var offset = this.isHorizontal() ?
      (this.ltr ? this.scroller.scrollLeft : this.maxSize - this.scroller.scrollLeft)
      : this.scroller.scrollTop;
  if (this.scrollSize < this.size) return;
  if (offset > this.scrollSize - this.size) {
    offset = this.scrollSize - this.size;
  }
  if (offset === this.scrollOffset) return;

  var itemSize = this.repeater.getItemSize();
  if (!itemSize) return;

  var numItems = Math.max(0, Math.floor(offset / itemSize) - NUM_EXTRA);

  var transform = (this.isHorizontal() ? 'translateX(' : 'translateY(') +
      (!this.isHorizontal() || this.ltr ? (numItems * itemSize) : - (numItems * itemSize))  + 'px)';

  this.scrollOffset = offset;
  this.offsetter.style.webkitTransform = transform;
  this.offsetter.style.transform = transform;

  if (this.bindTopIndex) {
    var topIndex = Math.floor(offset / itemSize);
    if (topIndex !== this.topIndex && topIndex < this.repeater.getItemCount()) {
      this.topIndex = topIndex;
      this.bindTopIndex.assign(this.$scope, topIndex);
      if (!this.$rootScope.$$phase) this.$scope.$digest();
    }
  }

  this.repeater.containerUpdated();
};


/**
 * @ngdoc directive
 * @name mdVirtualRepeat
 * @module material.components.virtualRepeat
 * @restrict A
 * @priority 1000
 * @description
 * The `md-virtual-repeat` attribute is applied to a template that is repeated using virtual
 * scrolling. This provides smooth and performant scrolling through very large lists of elements.
 *
 * Virtual repeat is a limited substitute for `ng-repeat` that renders only
 * enough DOM nodes to fill the container, recycling them as the user scrolls.
 *
 * ### Notes
 *
 * - Arrays are supported for iteration by default.
 * - An object can used use if `md-on-demand` is specified and the object implements the interface
 *   described in the `md-on-demand` <a ng-href="#attributes">documentation</a>.
 * - `trackBy`, `as` alias, and `(key, value)` syntax from `ng-repeat` are not supported.
 *
 * ### On-Demand Async Item Loading
 *
 * When using the `md-on-demand` attribute and loading some asynchronous data,
 * the `getItemAtIndex` function will mostly return nothing.
 *
 * <hljs lang="js">
 *   DynamicItems.prototype.getItemAtIndex = function(index) {
 *     if (this.pages[index]) {
 *       return this.pages[index];
 *     } else {
 *       // This is an asynchronous action and does not return any value.
 *       this.loadPage(index);
 *     }
 *   };
 * </hljs>
 *
 * This means that the Virtual Repeat will not have any value for the given index.<br/>
 * After the data loading completes, the user expects the Virtual Repeat to recognize the change.
 *
 * To make sure that the Virtual Repeat properly detects any change, you need to run the operation
 * in another digest.
 *
 * <hljs lang="js">
 *   DynamicItems.prototype.loadPage = function(index) {
 *     var self = this;
 *
 *     // Trigger a new digest by using $timeout
 *     $timeout(function() {
 *       self.pages[index] = Data;
 *     });
 *   };
 * </hljs>
 *
 * > <b>Note:</b> Please also review the
 *   <a ng-href="api/directive/mdVirtualRepeatContainer">VirtualRepeatContainer</a> documentation
 *   for more information.
 *
 * @usage
 * <hljs lang="html">
 * <md-virtual-repeat-container>
 *   <div md-virtual-repeat="i in items">Hello {{i}}!</div>
 * </md-virtual-repeat-container>
 *
 * <md-virtual-repeat-container md-orient-horizontal>
 *   <div md-virtual-repeat="i in items" md-item-size="20">Hello {{i}}!</div>
 * </md-virtual-repeat-container>
 * </hljs>
 *
 * @param {expression=} md-extra-name Evaluates to an additional name to which the current iterated
 *   item can be assigned on the repeated scope (needed for use in `md-autocomplete`).
 * @param {number=} md-item-size Optional height or width of the repeated elements (which **must be
 *   identical for each element**). Virtual repeat will attempt to read the size from the DOM,
 *   if missing, but it still assumes that all repeated nodes have the **same height**
 *   (when scrolling vertically) or **same width** (when scrolling horizontally).
 * @param {boolean=} md-on-demand When present, treats the `md-virtual-repeat` argument as an object
 *   that can fetch rows rather than an array.
 *
 *   **NOTE:** This object **must** implement the following interface with two methods:
 *
 *   - `getItemAtIndex` - `{function(index): Object}`: The item at that `index` or `null` if it is
 *     not yet loaded (it should start downloading the item in that case).
 *   - `getLength` - `{function(): number}`: The data length to which the repeater container
 *     should be sized. Ideally, when the count is known, this method should return it.
 *     Otherwise, return a higher number than the currently loaded items to produce an
 *     infinite-scroll behavior.
 */
function VirtualRepeatDirective($parse) {
  return {
    controller: VirtualRepeatController,
    priority: 1000,
    require: ['mdVirtualRepeat', '^^mdVirtualRepeatContainer'],
    restrict: 'A',
    terminal: true,
    transclude: 'element',
    compile: function VirtualRepeatCompile($element, $attrs) {
      var expression = $attrs.mdVirtualRepeat;
      var match = expression.match(/^\s*([\s\S]+?)\s+in\s+([\s\S]+?)\s*$/);
      var repeatName = match[1];
      var repeatListExpression = $parse(match[2]);
      var extraName = $attrs.mdExtraName && $parse($attrs.mdExtraName);

      return function VirtualRepeatLink($scope, $element, $attrs, ctrl, $transclude) {
        ctrl[0].link_(ctrl[1], $transclude, repeatName, repeatListExpression, extraName);
      };
    }
  };
}


/** @ngInject */
function VirtualRepeatController($scope, $element, $attrs, $browser, $document, $rootScope,
    $$rAF, $mdUtil) {
  this.$scope = $scope;
  this.$element = $element;
  this.$attrs = $attrs;
  this.$browser = $browser;
  this.$document = $document;
  this.$mdUtil = $mdUtil;
  this.$rootScope = $rootScope;
  this.$$rAF = $$rAF;

  /** @type {boolean} Whether we are in on-demand mode. */
  this.onDemand = $mdUtil.parseAttributeBoolean($attrs.mdOnDemand);
  /** @type {!Function} Backup reference to $browser.$$checkUrlChange */
  this.browserCheckUrlChange = $browser.$$checkUrlChange;
  /** @type {number} Most recent starting repeat index (based on scroll offset) */
  this.newStartIndex = 0;
  /** @type {number} Most recent ending repeat index (based on scroll offset) */
  this.newEndIndex = 0;
  /** @type {number} Most recent end visible index (based on scroll offset) */
  this.newVisibleEnd = 0;
  /** @type {number} Previous starting repeat index (based on scroll offset) */
  this.startIndex = 0;
  /** @type {number} Previous ending repeat index (based on scroll offset) */
  this.endIndex = 0;
  /** @type {?number} Height/width of repeated elements. */
  this.itemSize = $scope.$eval($attrs.mdItemSize) || null;

  /** @type {boolean} Whether this is the first time that items are rendered. */
  this.isFirstRender = true;

  /**
   * @private {boolean} Whether the items in the list are already being updated. Used to prevent
   *     nested calls to virtualRepeatUpdate_.
   */
  this.isVirtualRepeatUpdating_ = false;

  /** @type {number} Most recently seen length of items. */
  this.itemsLength = 0;

  /**
   * @type {!Function} Unwatch callback for item size (when md-items-size is
   *     not specified), or angular.noop otherwise.
   */
  this.unwatchItemSize_ = angular.noop;

  /**
   * Presently rendered blocks by repeat index.
   * @type {Object<number, !VirtualRepeatController.Block>}
   */
  this.blocks = {};
  /** @type {Array<!VirtualRepeatController.Block>} A pool of presently unused blocks. */
  this.pooledBlocks = [];

  $scope.$on('$destroy', angular.bind(this, this.cleanupBlocks_));
}


/**
 * An object representing a repeated item.
 * @typedef {{element: !jqLite, new: boolean, scope: !angular.Scope}}
 */
VirtualRepeatController.Block;


/**
 * Called at startup by the md-virtual-repeat postLink function.
 * @param {!VirtualRepeatContainerController} container The container's controller.
 * @param {!Function} transclude The repeated element's bound transclude function.
 * @param {string} repeatName The left hand side of the repeat expression, indicating
 *     the name for each item in the array.
 * @param {!Function} repeatListExpression A compiled expression based on the right hand side
 *     of the repeat expression. Points to the array to repeat over.
 * @param {string|undefined} extraName The optional extra repeatName.
 */
VirtualRepeatController.prototype.link_ =
    function(container, transclude, repeatName, repeatListExpression, extraName) {
  this.container = container;
  this.transclude = transclude;
  this.repeatName = repeatName;
  this.rawRepeatListExpression = repeatListExpression;
  this.extraName = extraName;
  this.sized = false;

  this.repeatListExpression = angular.bind(this, this.repeatListExpression_);

  this.container.register(this);
};


/** @private Cleans up unused blocks. */
VirtualRepeatController.prototype.cleanupBlocks_ = function() {
  angular.forEach(this.pooledBlocks, function cleanupBlock(block) {
    block.element.remove();
  });
};


/** @private Attempts to set itemSize by measuring a repeated element in the dom */
VirtualRepeatController.prototype.readItemSize_ = function() {
  if (this.itemSize) {
    // itemSize was successfully read in a different asynchronous call.
    return;
  }

  this.items = this.repeatListExpression(this.$scope);
  this.parentNode = this.$element[0].parentNode;
  var block = this.getBlock_(0);
  if (!block.element[0].parentNode) {
    this.parentNode.appendChild(block.element[0]);
  }

  this.itemSize = block.element[0][
      this.container.isHorizontal() ? 'offsetWidth' : 'offsetHeight'] || null;

  this.blocks[0] = block;
  this.poolBlock_(0);

  if (this.itemSize) {
    this.containerUpdated();
  }
};


/**
 * Returns the user-specified repeat list, transforming it into an array-like
 * object in the case of infinite scroll/dynamic load mode.
 * @param {!angular.Scope} scope The scope.
 * @return {!Array|!Object} An array or array-like object for iteration.
 */
VirtualRepeatController.prototype.repeatListExpression_ = function(scope) {
  var repeatList = this.rawRepeatListExpression(scope);

  if (this.onDemand && repeatList) {
    var virtualList = new VirtualRepeatModelArrayLike(repeatList);
    virtualList.$$includeIndexes(this.newStartIndex, this.newVisibleEnd);
    return virtualList;
  } else {
    return repeatList;
  }
};


/**
 * Called by the container. Informs us that the container's scroll or size has
 * changed.
 */
VirtualRepeatController.prototype.containerUpdated = function() {
  // If itemSize is unknown, attempt to measure it.
  if (!this.itemSize) {
    // Make sure to clean up watchers if we can (see #8178)
    if (this.unwatchItemSize_ && this.unwatchItemSize_ !== angular.noop){
      this.unwatchItemSize_();
    }
    this.unwatchItemSize_ = this.$scope.$watchCollection(
        this.repeatListExpression,
        angular.bind(this, function(items) {
          if (items && items.length) {
            this.readItemSize_();
          }
        }));
    if (!this.$rootScope.$$phase) this.$scope.$digest();

    return;
  } else if (!this.sized) {
    this.items = this.repeatListExpression(this.$scope);
  }

  if (!this.sized) {
    this.unwatchItemSize_();
    this.sized = true;
    this.$scope.$watchCollection(this.repeatListExpression,
        angular.bind(this, function(items, oldItems) {
          if (!this.isVirtualRepeatUpdating_) {
            this.virtualRepeatUpdate_(items, oldItems);
          }
        }));
  }

  this.updateIndexes_();

  if (this.newStartIndex !== this.startIndex ||
      this.newEndIndex !== this.endIndex ||
      this.container.getScrollOffset() > this.container.getScrollSize()) {
    if (this.items instanceof VirtualRepeatModelArrayLike) {
      this.items.$$includeIndexes(this.newStartIndex, this.newEndIndex);
    }
    this.virtualRepeatUpdate_(this.items, this.items);
  }
};


/**
 * Called by the container. Returns the size of a single repeated item.
 * @return {?number} size of a repeated item.
 */
VirtualRepeatController.prototype.getItemSize = function() {
  return this.itemSize;
};


/**
 * Called by the container.
 * @return {?number} the most recently seen length of items.
 */
VirtualRepeatController.prototype.getItemCount = function() {
  return this.itemsLength;
};


/**
 * Updates the order and visible offset of repeated blocks in response to scrolling
 * or updates to `items`.
 * @param {Array} items visible elements
 * @param {Array} oldItems previously visible elements
 * @private
 */
VirtualRepeatController.prototype.virtualRepeatUpdate_ = function(items, oldItems) {
  this.isVirtualRepeatUpdating_ = true;

  var itemsLength = items && items.length || 0;
  var lengthChanged = false;

  // If the number of items shrank, keep the scroll position.
  if (this.items && itemsLength < this.items.length && this.container.getScrollOffset() !== 0) {
    this.items = items;
    var previousScrollOffset = this.container.getScrollOffset();
    this.container.resetScroll();
    this.container.scrollTo(previousScrollOffset);
  }

  if (itemsLength !== this.itemsLength) {
    lengthChanged = true;
    this.itemsLength = itemsLength;
  }

  this.items = items;
  if (items !== oldItems || lengthChanged) {
    this.updateIndexes_();
  }

  this.parentNode = this.$element[0].parentNode;

  if (lengthChanged) {
    this.container.setScrollSize(itemsLength * this.itemSize);
  }

  // Detach and pool any blocks that are no longer in the viewport.
  Object.keys(this.blocks).forEach(function(blockIndex) {
    var index = parseInt(blockIndex, 10);
    if (index < this.newStartIndex || index >= this.newEndIndex) {
      this.poolBlock_(index);
    }
  }, this);

  // Add needed blocks.
  // For performance reasons, temporarily block browser url checks as we digest
  // the restored block scopes ($$checkUrlChange reads window.location to
  // check for changes and trigger route change, etc, which we don't need when
  // trying to scroll at 60fps).
  this.$browser.$$checkUrlChange = angular.noop;

  var i, block,
      newStartBlocks = [],
      newEndBlocks = [];

  // Collect blocks at the top.
  for (i = this.newStartIndex; i < this.newEndIndex && this.blocks[i] == null; i++) {
    block = this.getBlock_(i);
    this.updateBlock_(block, i);
    newStartBlocks.push(block);
  }

  // Update blocks that are already rendered.
  for (; this.blocks[i] != null; i++) {
    this.updateBlock_(this.blocks[i], i);
  }
  var maxIndex = i - 1;

  // Collect blocks at the end.
  for (; i < this.newEndIndex; i++) {
    block = this.getBlock_(i);
    this.updateBlock_(block, i);
    newEndBlocks.push(block);
  }

  // Attach collected blocks to the document.
  if (newStartBlocks.length) {
    this.parentNode.insertBefore(
        this.domFragmentFromBlocks_(newStartBlocks),
        this.$element[0].nextSibling);
  }
  if (newEndBlocks.length) {
    this.parentNode.insertBefore(
        this.domFragmentFromBlocks_(newEndBlocks),
        this.blocks[maxIndex] && this.blocks[maxIndex].element[0].nextSibling);
  }

  // Restore $$checkUrlChange.
  this.$browser.$$checkUrlChange = this.browserCheckUrlChange;

  this.startIndex = this.newStartIndex;
  this.endIndex = this.newEndIndex;

  if (this.isFirstRender) {
    this.isFirstRender = false;
    var firstRenderStartIndex = this.$attrs.mdStartIndex ?
      this.$scope.$eval(this.$attrs.mdStartIndex) :
      this.container.topIndex;

    // The first call to virtualRepeatUpdate_ may not be when the virtual repeater is ready.
    // Introduce a slight delay so that the update happens when it is actually ready.
    this.$mdUtil.nextTick(function() {
      this.container.scrollToIndex(firstRenderStartIndex);
    }.bind(this));
  }

  this.isVirtualRepeatUpdating_ = false;
};


/**
 * @param {number} index Where the block is to be in the repeated list.
 * @return {!VirtualRepeatController.Block} A new or pooled block to place at the specified index.
 * @private
 */
VirtualRepeatController.prototype.getBlock_ = function(index) {
  if (this.pooledBlocks.length) {
    return this.pooledBlocks.pop();
  }

  var block;
  this.transclude(angular.bind(this, function(clone, scope) {
    block = {
      element: clone,
      new: true,
      scope: scope
    };

    this.updateScope_(scope, index);
    this.parentNode.appendChild(clone[0]);
  }));

  return block;
};


/**
 * Updates and if not in a digest cycle, digests the specified block's scope to the data
 * at the specified index.
 * @param {!VirtualRepeatController.Block} block The block whose scope should be updated.
 * @param {number} index The index to set.
 * @private
 */
VirtualRepeatController.prototype.updateBlock_ = function(block, index) {
  this.blocks[index] = block;

  if (!block.new &&
      (block.scope.$index === index && block.scope[this.repeatName] === this.items[index])) {
    return;
  }
  block.new = false;

  // Update and digest the block's scope.
  this.updateScope_(block.scope, index);

  // Perform digest before reattaching the block.
  // Any resulting synchronous DOM mutations should be much faster as a result.
  // This might break some directives.
  if (!this.$rootScope.$$phase) {
    block.scope.$digest();
  }
};


/**
 * Updates scope to the data at the specified index.
 * @param {!angular.Scope} scope The scope which should be updated.
 * @param {number} index The index to set.
 * @private
 */
VirtualRepeatController.prototype.updateScope_ = function(scope, index) {
  scope.$index = index;
  scope[this.repeatName] = this.items && this.items[index];
  if (this.extraName) scope[this.extraName(this.$scope)] = this.items[index];
};


/**
 * Pools the block at the specified index (Pulls its element out of the dom and stores it).
 * @param {number} index The index at which the block to pool is stored.
 * @private
 */
VirtualRepeatController.prototype.poolBlock_ = function(index) {
  this.pooledBlocks.push(this.blocks[index]);
  this.parentNode.removeChild(this.blocks[index].element[0]);
  delete this.blocks[index];
};


/**
 * Produces a dom fragment containing the elements from the list of blocks.
 * @param {!Array<!VirtualRepeatController.Block>} blocks The blocks whose elements
 *     should be added to the document fragment.
 * @return {DocumentFragment}
 * @private
 */
VirtualRepeatController.prototype.domFragmentFromBlocks_ = function(blocks) {
  var fragment = this.$document[0].createDocumentFragment();
  blocks.forEach(function(block) {
    fragment.appendChild(block.element[0]);
  });
  return fragment;
};


/**
 * Updates start and end indexes based on length of repeated items and container size.
 * @private
 */
VirtualRepeatController.prototype.updateIndexes_ = function() {
  var itemsLength = this.items ? this.items.length : 0;
  var containerLength = Math.ceil(this.container.getSize() / this.itemSize);

  this.newStartIndex = Math.max(0, Math.min(
      itemsLength - containerLength,
      Math.floor(this.container.getScrollOffset() / this.itemSize)));
  this.newVisibleEnd = this.newStartIndex + containerLength + NUM_EXTRA;
  this.newEndIndex = Math.min(itemsLength, this.newVisibleEnd);
  this.newStartIndex = Math.max(0, this.newStartIndex - NUM_EXTRA);
};

/**
 * This VirtualRepeatModelArrayLike class enforces the interface requirements
 * for infinite scrolling within a mdVirtualRepeatContainer.
 *
 * @param {Object} model An object with this interface must implement the following interface with
 * two (2) methods:
 *
 * getItemAtIndex: function(index) -> item at that index or null if it is not yet
 *     loaded (It should start downloading the item in that case).
 *
 * getLength: function() -> number The data length to which the repeater container
 *     should be sized. Ideally, when the count is known, this method should return it.
 *     Otherwise, return a higher number than the currently loaded items to produce an
 *     infinite-scroll behavior.
 *
 * @usage
 * <hljs lang="html">
 *  <md-virtual-repeat-container md-orient-horizontal>
 *    <div md-virtual-repeat="i in items" md-on-demand>
 *      Hello {{i}}!
 *    </div>
 *  </md-virtual-repeat-container>
 * </hljs>
 *
 */
function VirtualRepeatModelArrayLike(model) {
  if (!angular.isFunction(model.getItemAtIndex) ||
      !angular.isFunction(model.getLength)) {
    throw Error('When md-on-demand is enabled, the Object passed to md-virtual-repeat must ' +
        'implement functions getItemAtIndex() and getLength().');
  }

  this.model = model;
}

/**
 * @param {number} start
 * @param {number} end
 */
VirtualRepeatModelArrayLike.prototype.$$includeIndexes = function(start, end) {
  for (var i = start; i < end; i++) {
    if (!this.hasOwnProperty(i)) {
      this[i] = this.model.getItemAtIndex(i);
    }
  }
  this.length = this.model.getLength();
};

/**
 * @ngdoc directive
 * @name mdForceHeight
 * @module material.components.virtualRepeat
 * @restrict A
 * @description
 *
 * Force an element to have a certain `px` height. This is used in place of a style tag in order to
 * conform to the
 * <a href="https://developer.mozilla.org/docs/Web/HTTP/Headers/Content-Security-Policy/script-src">
 *   Content Security Policy</a> regarding `unsafe-inline` `<style>` tags.
 *
 * This directive is related to <a ng-href="api/directive/mdVirtualRepeat">mdVirtualRepeat</a>.
 *
 * @usage
 * <hljs lang="html">
 *   <div md-force-height="'100px'"></div>
 * </hljs>
 */
function ForceHeightDirective($mdUtil) {
  return {
    restrict: 'A',
    link: function(scope, element, attrs) {
      var height = scope.$eval(attrs.mdForceHeight) || null;

      if (height && element) {
        element[0].style.height = height;
      }
    }
  };
}
ForceHeightDirective.$inject = ['$mdUtil'];

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.whiteframe
 */
MdWhiteframeDirective.$inject = ["$log"];
angular
  .module('material.components.whiteframe', ['material.core'])
  .directive('mdWhiteframe', MdWhiteframeDirective);

/**
 * @ngdoc directive
 * @module material.components.whiteframe
 * @name mdWhiteframe
 *
 * @description
 * The md-whiteframe directive allows you to apply an elevation shadow to an element.
 *
 * The attribute values needs to be a number between 1 and 24 or -1.
 * When set to -1 no style is applied.
 *
 * ### Notes
 * - If there is no value specified it defaults to 4dp.
 * - If the value is not valid it defaults to 4dp.

 * @usage
 * <hljs lang="html">
 * <div md-whiteframe="3">
 *   <span>Elevation of 3dp</span>
 * </div>
 * </hljs>
 *
 * <hljs lang="html">
 * <div md-whiteframe="-1">
 *   <span>No elevation shadow applied</span>
 * </div>
 * </hljs>
 *
 * <hljs lang="html">
 * <div ng-init="elevation = 5" md-whiteframe="{{elevation}}">
 *   <span>Elevation of 5dp with an interpolated value</span>
 * </div>
 * </hljs>
 */
function MdWhiteframeDirective($log) {
  var DISABLE_DP = -1;
  var MIN_DP = 1;
  var MAX_DP = 24;
  var DEFAULT_DP = 4;

  return {
    link: postLink
  };

  function postLink(scope, element, attr) {
    var oldClass = '';

    attr.$observe('mdWhiteframe', function(elevation) {
      elevation = parseInt(elevation, 10) || DEFAULT_DP;

      if (elevation != DISABLE_DP && (elevation > MAX_DP || elevation < MIN_DP)) {
        $log.warn('md-whiteframe attribute value is invalid. It should be a number between ' + MIN_DP + ' and ' + MAX_DP, element[0]);
        elevation = DEFAULT_DP;
      }

      var newClass = elevation == DISABLE_DP ? '' : 'md-whiteframe-' + elevation + 'dp';
      attr.$updateClass(newClass, oldClass);
      oldClass = newClass;
    });
  }
}


})();
(function(){ 
angular.module("material.core").constant("$MD_THEME_CSS", "md-autocomplete.md-THEME_NAME-theme{background:\"{{background-hue-1}}\"}md-autocomplete.md-THEME_NAME-theme[disabled]:not([md-floating-label]){background:\"{{background-hue-2}}\"}md-autocomplete.md-THEME_NAME-theme button md-icon path{fill:\"{{background-600}}\"}md-autocomplete.md-THEME_NAME-theme button:after{background:\"{{background-600-0.3}}\"}md-autocomplete.md-THEME_NAME-theme input{color:\"{{foreground-1}}\"}md-autocomplete.md-THEME_NAME-theme.md-accent md-input-container.md-input-focused .md-input{border-color:\"{{accent-color}}\"}md-autocomplete.md-THEME_NAME-theme.md-accent md-input-container.md-input-focused label,md-autocomplete.md-THEME_NAME-theme.md-accent md-input-container.md-input-focused md-icon{color:\"{{accent-color}}\"}md-autocomplete.md-THEME_NAME-theme.md-accent md-progress-linear .md-container{background-color:\"{{accent-100}}\"}md-autocomplete.md-THEME_NAME-theme.md-accent md-progress-linear .md-bar{background-color:\"{{accent-color}}\"}md-autocomplete.md-THEME_NAME-theme.md-warn md-input-container.md-input-focused .md-input{border-color:\"{{warn-A700}}\"}md-autocomplete.md-THEME_NAME-theme.md-warn md-input-container.md-input-focused label,md-autocomplete.md-THEME_NAME-theme.md-warn md-input-container.md-input-focused md-icon{color:\"{{warn-A700}}\"}md-autocomplete.md-THEME_NAME-theme.md-warn md-progress-linear .md-container{background-color:\"{{warn-100}}\"}md-autocomplete.md-THEME_NAME-theme.md-warn md-progress-linear .md-bar{background-color:\"{{warn-color}}\"}.md-autocomplete-standard-list-container.md-THEME_NAME-theme,.md-autocomplete-suggestions-container.md-THEME_NAME-theme{background:\"{{background-hue-1}}\"}.md-autocomplete-standard-list-container.md-THEME_NAME-theme .md-autocomplete-suggestion,.md-autocomplete-suggestions-container.md-THEME_NAME-theme .md-autocomplete-suggestion{color:\"{{foreground-1}}\"}.md-autocomplete-standard-list-container.md-THEME_NAME-theme .md-autocomplete-suggestion.selected,.md-autocomplete-standard-list-container.md-THEME_NAME-theme .md-autocomplete-suggestion:hover,.md-autocomplete-suggestions-container.md-THEME_NAME-theme .md-autocomplete-suggestion.selected,.md-autocomplete-suggestions-container.md-THEME_NAME-theme .md-autocomplete-suggestion:hover{background:\"{{background-500-0.18}}\"}md-backdrop{background-color:\"{{background-900-0.0}}\"}md-backdrop.md-opaque.md-THEME_NAME-theme{background-color:\"{{background-900-1.0}}\"}md-bottom-sheet.md-THEME_NAME-theme{background-color:\"{{background-color}}\";border-top-color:\"{{background-hue-3}}\"}md-bottom-sheet.md-THEME_NAME-theme.md-list md-list-item{color:\"{{foreground-1}}\"}md-bottom-sheet.md-THEME_NAME-theme .md-subheader{background-color:\"{{background-color}}\";color:\"{{foreground-1}}\"}.md-button.md-THEME_NAME-theme:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme:not([disabled]):hover{background-color:\"{{background-500-0.2}}\"}.md-button.md-THEME_NAME-theme:not([disabled]).md-icon-button:hover{background-color:transparent}.md-button.md-THEME_NAME-theme.md-fab md-icon{color:\"{{accent-contrast}}\"}.md-button.md-THEME_NAME-theme.md-primary{color:\"{{primary-color}}\"}.md-button.md-THEME_NAME-theme.md-primary.md-fab,.md-button.md-THEME_NAME-theme.md-primary.md-raised{color:\"{{primary-contrast}}\";background-color:\"{{primary-color}}\"}.md-button.md-THEME_NAME-theme.md-primary.md-fab:not([disabled]) md-icon,.md-button.md-THEME_NAME-theme.md-primary.md-raised:not([disabled]) md-icon{color:\"{{primary-contrast}}\"}.md-button.md-THEME_NAME-theme.md-primary.md-fab:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-primary.md-fab:not([disabled]):hover,.md-button.md-THEME_NAME-theme.md-primary.md-raised:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-primary.md-raised:not([disabled]):hover{background-color:\"{{primary-600}}\"}.md-button.md-THEME_NAME-theme.md-primary:not([disabled]) md-icon{color:\"{{primary-color}}\"}.md-button.md-THEME_NAME-theme.md-fab{background-color:\"{{accent-color}}\";color:\"{{accent-contrast}}\"}.md-button.md-THEME_NAME-theme.md-fab:not([disabled]) .md-icon{color:\"{{accent-contrast}}\"}.md-button.md-THEME_NAME-theme.md-fab:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-fab:not([disabled]):hover{background-color:\"{{accent-A700}}\"}.md-button.md-THEME_NAME-theme.md-raised{color:\"{{background-900}}\";background-color:\"{{background-50}}\"}.md-button.md-THEME_NAME-theme.md-raised:not([disabled]) md-icon{color:\"{{background-900}}\"}.md-button.md-THEME_NAME-theme.md-raised:not([disabled]):hover{background-color:\"{{background-50}}\"}.md-button.md-THEME_NAME-theme.md-raised:not([disabled]).md-focused{background-color:\"{{background-200}}\"}.md-button.md-THEME_NAME-theme.md-warn{color:\"{{warn-color}}\"}.md-button.md-THEME_NAME-theme.md-warn.md-fab,.md-button.md-THEME_NAME-theme.md-warn.md-raised{color:\"{{warn-contrast}}\";background-color:\"{{warn-color}}\"}.md-button.md-THEME_NAME-theme.md-warn.md-fab:not([disabled]) md-icon,.md-button.md-THEME_NAME-theme.md-warn.md-raised:not([disabled]) md-icon{color:\"{{warn-contrast}}\"}.md-button.md-THEME_NAME-theme.md-warn.md-fab:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-warn.md-fab:not([disabled]):hover,.md-button.md-THEME_NAME-theme.md-warn.md-raised:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-warn.md-raised:not([disabled]):hover{background-color:\"{{warn-600}}\"}.md-button.md-THEME_NAME-theme.md-warn:not([disabled]) md-icon{color:\"{{warn-color}}\"}.md-button.md-THEME_NAME-theme.md-accent{color:\"{{accent-color}}\"}.md-button.md-THEME_NAME-theme.md-accent.md-fab,.md-button.md-THEME_NAME-theme.md-accent.md-raised{color:\"{{accent-contrast}}\";background-color:\"{{accent-color}}\"}.md-button.md-THEME_NAME-theme.md-accent.md-fab:not([disabled]) md-icon,.md-button.md-THEME_NAME-theme.md-accent.md-raised:not([disabled]) md-icon{color:\"{{accent-contrast}}\"}.md-button.md-THEME_NAME-theme.md-accent.md-fab:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-accent.md-fab:not([disabled]):hover,.md-button.md-THEME_NAME-theme.md-accent.md-raised:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-accent.md-raised:not([disabled]):hover{background-color:\"{{accent-A700}}\"}.md-button.md-THEME_NAME-theme.md-accent:not([disabled]) md-icon{color:\"{{accent-color}}\"}.md-button.md-THEME_NAME-theme.md-accent[disabled],.md-button.md-THEME_NAME-theme.md-fab[disabled],.md-button.md-THEME_NAME-theme.md-raised[disabled],.md-button.md-THEME_NAME-theme.md-warn[disabled],.md-button.md-THEME_NAME-theme[disabled]{color:\"{{foreground-3}}\";cursor:default}.md-button.md-THEME_NAME-theme.md-accent[disabled] md-icon,.md-button.md-THEME_NAME-theme.md-fab[disabled] md-icon,.md-button.md-THEME_NAME-theme.md-raised[disabled] md-icon,.md-button.md-THEME_NAME-theme.md-warn[disabled] md-icon,.md-button.md-THEME_NAME-theme[disabled] md-icon{color:\"{{foreground-3}}\"}.md-button.md-THEME_NAME-theme.md-fab[disabled],.md-button.md-THEME_NAME-theme.md-raised[disabled]{background-color:\"{{foreground-4}}\"}.md-button.md-THEME_NAME-theme[disabled]{background-color:transparent}._md a.md-THEME_NAME-theme:not(.md-button).md-primary{color:\"{{primary-color}}\"}._md a.md-THEME_NAME-theme:not(.md-button).md-primary:hover{color:\"{{primary-700}}\"}._md a.md-THEME_NAME-theme:not(.md-button).md-accent{color:\"{{accent-color}}\"}._md a.md-THEME_NAME-theme:not(.md-button).md-accent:hover{color:\"{{accent-A700}}\"}._md a.md-THEME_NAME-theme:not(.md-button).md-warn{color:\"{{warn-color}}\"}._md a.md-THEME_NAME-theme:not(.md-button).md-warn:hover{color:\"{{warn-700}}\"}md-card.md-THEME_NAME-theme{color:\"{{foreground-1}}\";background-color:\"{{background-hue-1}}\";border-radius:2px}md-card.md-THEME_NAME-theme .md-card-image{border-radius:2px 2px 0 0}md-card.md-THEME_NAME-theme md-card-header md-card-avatar md-icon{color:\"{{background-color}}\";background-color:\"{{foreground-3}}\"}md-card.md-THEME_NAME-theme md-card-header md-card-header-text .md-subhead,md-card.md-THEME_NAME-theme md-card-title md-card-title-text:not(:only-child) .md-subhead{color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme .md-ripple{color:\"{{accent-A700}}\"}md-checkbox.md-THEME_NAME-theme.md-checked .md-ripple{color:\"{{background-600}}\"}md-checkbox.md-THEME_NAME-theme.md-checked.md-focused .md-container:before{background-color:\"{{accent-color-0.26}}\"}md-checkbox.md-THEME_NAME-theme .md-ink-ripple{color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme.md-checked .md-ink-ripple{color:\"{{accent-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not(.md-checked) .md-icon{border-color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme.md-checked .md-icon{background-color:\"{{accent-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme.md-checked .md-icon:after{border-color:\"{{accent-contrast-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary .md-ripple{color:\"{{primary-600}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-ripple{color:\"{{background-600}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary .md-ink-ripple{color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-ink-ripple{color:\"{{primary-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary:not(.md-checked) .md-icon{border-color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-icon{background-color:\"{{primary-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked.md-focused .md-container:before{background-color:\"{{primary-color-0.26}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-icon:after{border-color:\"{{primary-contrast-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary .md-indeterminate[disabled] .md-container{color:\"{{foreground-3}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn .md-ripple{color:\"{{warn-600}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn .md-ink-ripple{color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-ink-ripple{color:\"{{warn-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn:not(.md-checked) .md-icon{border-color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-icon{background-color:\"{{warn-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked.md-focused:not([disabled]) .md-container:before{background-color:\"{{warn-color-0.26}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-icon:after{border-color:\"{{background-200}}\"}md-checkbox.md-THEME_NAME-theme[disabled]:not(.md-checked) .md-icon{border-color:\"{{foreground-3}}\"}md-checkbox.md-THEME_NAME-theme[disabled].md-checked .md-icon{background-color:\"{{foreground-3}}\"}md-checkbox.md-THEME_NAME-theme[disabled].md-checked .md-icon:after{border-color:\"{{background-200}}\"}md-checkbox.md-THEME_NAME-theme[disabled] .md-icon:after{border-color:\"{{foreground-3}}\"}md-checkbox.md-THEME_NAME-theme[disabled] .md-label{color:\"{{foreground-3}}\"}md-chips.md-THEME_NAME-theme .md-chips{box-shadow:0 1px \"{{foreground-4}}\"}md-chips.md-THEME_NAME-theme .md-chips.md-focused{box-shadow:0 2px \"{{primary-color}}\"}md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input{color:\"{{foreground-1}}\"}md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input::-webkit-input-placeholder{color:\"{{foreground-3}}\"}md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input:-ms-input-placeholder,md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input::-ms-input-placeholder{color:\"{{foreground-3}}\"}md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input::placeholder{color:\"{{foreground-3}}\"}md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input:-moz-placeholder,md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input::-moz-placeholder{color:\"{{foreground-3}}\";opacity:1}md-chips.md-THEME_NAME-theme md-chip{background:\"{{background-300}}\";color:\"{{background-800}}\"}md-chips.md-THEME_NAME-theme md-chip md-icon{color:\"{{background-700}}\"}md-chips.md-THEME_NAME-theme md-chip.md-focused{background:\"{{primary-color}}\";color:\"{{primary-contrast}}\"}md-chips.md-THEME_NAME-theme md-chip.md-focused md-icon{color:\"{{primary-contrast}}\"}md-chips.md-THEME_NAME-theme md-chip._md-chip-editing{background:transparent;color:\"{{background-800}}\"}md-chips.md-THEME_NAME-theme md-chip-remove .md-button md-icon path{fill:\"{{background-500}}\"}.md-contact-suggestion span.md-contact-email{color:\"{{background-400}}\"}md-content.md-THEME_NAME-theme{color:\"{{foreground-1}}\";background-color:\"{{background-default}}\"}.md-THEME_NAME-theme .md-calendar{background:\"{{background-hue-1}}\";color:\"{{foreground-1-0.87}}\"}.md-THEME_NAME-theme .md-calendar tr:last-child td{border-bottom-color:\"{{background-hue-2}}\"}.md-THEME_NAME-theme .md-calendar-day-header{background:\"{{background-500-0.32}}\";color:\"{{foreground-1-0.87}}\"}.md-THEME_NAME-theme .md-calendar-date.md-calendar-date-today .md-calendar-date-selection-indicator{border:1px solid \"{{primary-500}}\"}.md-THEME_NAME-theme .md-calendar-date.md-calendar-date-today.md-calendar-date-disabled{color:\"{{primary-500-0.6}}\"}.md-calendar-date.md-focus .md-THEME_NAME-theme .md-calendar-date-selection-indicator,.md-THEME_NAME-theme .md-calendar-date-selection-indicator:hover{background:\"{{background-500-0.32}}\"}.md-THEME_NAME-theme .md-calendar-date.md-calendar-selected-date .md-calendar-date-selection-indicator,.md-THEME_NAME-theme .md-calendar-date.md-focus.md-calendar-selected-date .md-calendar-date-selection-indicator{background:\"{{primary-500}}\";color:\"{{primary-500-contrast}}\";border-color:transparent}.md-THEME_NAME-theme .md-calendar-date-disabled,.md-THEME_NAME-theme .md-calendar-month-label-disabled{color:\"{{foreground-3}}\"}.md-THEME_NAME-theme .md-calendar-month-label md-icon,.md-THEME_NAME-theme .md-datepicker-input{color:\"{{foreground-1}}\"}.md-THEME_NAME-theme .md-datepicker-input::-webkit-input-placeholder{color:\"{{foreground-3}}\"}.md-THEME_NAME-theme .md-datepicker-input:-ms-input-placeholder,.md-THEME_NAME-theme .md-datepicker-input::-ms-input-placeholder{color:\"{{foreground-3}}\"}.md-THEME_NAME-theme .md-datepicker-input::placeholder{color:\"{{foreground-3}}\"}.md-THEME_NAME-theme .md-datepicker-input:-moz-placeholder,.md-THEME_NAME-theme .md-datepicker-input::-moz-placeholder{color:\"{{foreground-3}}\";opacity:1}.md-THEME_NAME-theme .md-datepicker-input-container{border-bottom-color:\"{{foreground-4}}\"}.md-THEME_NAME-theme .md-datepicker-input-container.md-datepicker-focused{border-bottom-color:\"{{primary-color}}\"}.md-accent .md-THEME_NAME-theme .md-datepicker-input-container.md-datepicker-focused{border-bottom-color:\"{{accent-color}}\"}.md-THEME_NAME-theme .md-datepicker-input-container.md-datepicker-invalid,.md-warn .md-THEME_NAME-theme .md-datepicker-input-container.md-datepicker-focused{border-bottom-color:\"{{warn-A700}}\"}.md-THEME_NAME-theme .md-datepicker-calendar-pane{border-color:\"{{background-hue-1}}\"}.md-THEME_NAME-theme .md-datepicker-triangle-button .md-datepicker-expand-triangle{border-top-color:\"{{foreground-2}}\"}.md-THEME_NAME-theme .md-datepicker-open .md-datepicker-calendar-icon{color:\"{{primary-color}}\"}.md-accent .md-THEME_NAME-theme .md-datepicker-open .md-datepicker-calendar-icon,.md-THEME_NAME-theme .md-datepicker-open.md-accent .md-datepicker-calendar-icon{color:\"{{accent-color}}\"}.md-THEME_NAME-theme .md-datepicker-open.md-warn .md-datepicker-calendar-icon,.md-warn .md-THEME_NAME-theme .md-datepicker-open .md-datepicker-calendar-icon{color:\"{{warn-A700}}\"}.md-THEME_NAME-theme .md-datepicker-calendar{background:\"{{background-hue-1}}\"}.md-THEME_NAME-theme .md-datepicker-input-mask-opaque{box-shadow:0 0 0 9999px \"{{background-hue-1}}\"}.md-THEME_NAME-theme .md-datepicker-open .md-datepicker-input-container{background:\"{{background-hue-1}}\"}md-dialog.md-THEME_NAME-theme{border-radius:4px;background-color:\"{{background-hue-1}}\";color:\"{{foreground-1}}\"}md-dialog.md-THEME_NAME-theme.md-content-overflow .md-actions,md-dialog.md-THEME_NAME-theme.md-content-overflow md-dialog-actions,md-divider.md-THEME_NAME-theme{border-top-color:\"{{foreground-4}}\"}.layout-gt-lg-row>md-divider.md-THEME_NAME-theme,.layout-gt-md-row>md-divider.md-THEME_NAME-theme,.layout-gt-sm-row>md-divider.md-THEME_NAME-theme,.layout-gt-xs-row>md-divider.md-THEME_NAME-theme,.layout-lg-row>md-divider.md-THEME_NAME-theme,.layout-md-row>md-divider.md-THEME_NAME-theme,.layout-row>md-divider.md-THEME_NAME-theme,.layout-sm-row>md-divider.md-THEME_NAME-theme,.layout-xl-row>md-divider.md-THEME_NAME-theme,.layout-xs-row>md-divider.md-THEME_NAME-theme{border-right-color:\"{{foreground-4}}\"}md-icon.md-THEME_NAME-theme{color:\"{{foreground-2}}\"}md-icon.md-THEME_NAME-theme.md-primary{color:\"{{primary-color}}\"}md-icon.md-THEME_NAME-theme.md-accent{color:\"{{accent-color}}\"}md-icon.md-THEME_NAME-theme.md-warn{color:\"{{warn-color}}\"}md-input-container.md-THEME_NAME-theme .md-input{color:\"{{foreground-1}}\";border-color:\"{{foreground-4}}\"}md-input-container.md-THEME_NAME-theme .md-input::-webkit-input-placeholder{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme .md-input:-ms-input-placeholder,md-input-container.md-THEME_NAME-theme .md-input::-ms-input-placeholder{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme .md-input::placeholder{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme .md-input:-moz-placeholder,md-input-container.md-THEME_NAME-theme .md-input::-moz-placeholder{color:\"{{foreground-2}}\";opacity:1}md-input-container.md-THEME_NAME-theme>md-icon{color:\"{{foreground-1}}\"}md-input-container.md-THEME_NAME-theme .md-placeholder,md-input-container.md-THEME_NAME-theme label{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme label.md-required:after{color:\"{{warn-A700}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-focused):not(.md-input-invalid) label.md-required:after{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme .md-input-message-animation,md-input-container.md-THEME_NAME-theme .md-input-messages-animation{color:\"{{warn-A700}}\"}md-input-container.md-THEME_NAME-theme .md-input-message-animation .md-char-counter,md-input-container.md-THEME_NAME-theme .md-input-messages-animation .md-char-counter{color:\"{{foreground-1}}\"}md-input-container.md-THEME_NAME-theme.md-input-focused .md-input::-webkit-input-placeholder{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme.md-input-focused .md-input:-ms-input-placeholder,md-input-container.md-THEME_NAME-theme.md-input-focused .md-input::-ms-input-placeholder{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme.md-input-focused .md-input::placeholder{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme.md-input-focused .md-input:-moz-placeholder,md-input-container.md-THEME_NAME-theme.md-input-focused .md-input::-moz-placeholder{color:\"{{foreground-2}}\";opacity:1}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-has-value label{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused .md-input,md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-resized .md-input{border-color:\"{{primary-color}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused label,md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused md-icon{color:\"{{primary-color}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-accent .md-input{border-color:\"{{accent-color}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-accent label,md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-accent md-icon{color:\"{{accent-color}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-warn .md-input{border-color:\"{{warn-A700}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-warn label,md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-warn md-icon{color:\"{{warn-A700}}\"}md-input-container.md-THEME_NAME-theme.md-input-invalid .md-input{border-color:\"{{warn-A700}}\"}md-input-container.md-THEME_NAME-theme.md-input-invalid .md-char-counter,md-input-container.md-THEME_NAME-theme.md-input-invalid .md-input-message-animation,md-input-container.md-THEME_NAME-theme.md-input-invalid label{color:\"{{warn-A700}}\"}[disabled] md-input-container.md-THEME_NAME-theme .md-input,md-input-container.md-THEME_NAME-theme .md-input[disabled]{border-bottom-color:transparent;color:\"{{foreground-3}}\";background-image:linear-gradient(90deg,\"{{foreground-3}}\" 0,\"{{foreground-3}}\" 33%,transparent 0);background-image:-ms-linear-gradient(left,transparent 0,\"{{foreground-3}}\" 100%)}md-list.md-THEME_NAME-theme md-list-item.md-2-line .md-list-item-text h3,md-list.md-THEME_NAME-theme md-list-item.md-2-line .md-list-item-text h4,md-list.md-THEME_NAME-theme md-list-item.md-3-line .md-list-item-text h3,md-list.md-THEME_NAME-theme md-list-item.md-3-line .md-list-item-text h4{color:\"{{foreground-1}}\"}md-list.md-THEME_NAME-theme md-list-item.md-2-line .md-list-item-text p,md-list.md-THEME_NAME-theme md-list-item.md-3-line .md-list-item-text p{color:\"{{foreground-2}}\"}md-list.md-THEME_NAME-theme .md-proxy-focus.md-focused div.md-no-style{background-color:\"{{background-100}}\"}md-list.md-THEME_NAME-theme md-list-item .md-avatar-icon{background-color:\"{{foreground-3}}\";color:\"{{background-color}}\"}md-list.md-THEME_NAME-theme md-list-item>md-icon{color:\"{{foreground-2}}\"}md-list.md-THEME_NAME-theme md-list-item>md-icon.md-highlight{color:\"{{primary-color}}\"}md-list.md-THEME_NAME-theme md-list-item>md-icon.md-highlight.md-accent{color:\"{{accent-color}}\"}md-menu-content.md-THEME_NAME-theme{background-color:\"{{background-hue-1}}\"}md-menu-content.md-THEME_NAME-theme md-menu-item{color:\"{{foreground-1}}\"}md-menu-content.md-THEME_NAME-theme md-menu-item md-icon{color:\"{{foreground-2}}\"}md-menu-content.md-THEME_NAME-theme md-menu-item .md-button[disabled],md-menu-content.md-THEME_NAME-theme md-menu-item .md-button[disabled] md-icon{color:\"{{foreground-3}}\"}md-menu-content.md-THEME_NAME-theme md-menu-divider{background-color:\"{{foreground-4}}\"}md-menu-bar.md-THEME_NAME-theme>button.md-button{color:\"{{foreground-1}}\";border-radius:2px}md-menu-bar.md-THEME_NAME-theme md-menu>button{color:\"{{foreground-1}}\"}md-menu-bar.md-THEME_NAME-theme md-menu.md-open>button,md-menu-bar.md-THEME_NAME-theme md-menu>button:focus{outline:none;background-color:\"{{ background-500-0.18}}\"}md-menu-bar.md-THEME_NAME-theme.md-open:not(.md-keyboard-mode) md-menu:hover>button{background-color:\"{{ background-500-0.18}}\"}md-menu-bar.md-THEME_NAME-theme:not(.md-keyboard-mode):not(.md-open) md-menu button:focus,md-menu-bar.md-THEME_NAME-theme:not(.md-keyboard-mode):not(.md-open) md-menu button:hover{background:transparent}md-menu-content.md-THEME_NAME-theme .md-menu>.md-button:after{color:\"{{foreground-2}}\"}md-menu-content.md-THEME_NAME-theme .md-menu.md-open>.md-button{background-color:\"{{ background-500-0.18}}\"}md-toolbar.md-THEME_NAME-theme.md-menu-toolbar{background-color:\"{{background-hue-1}}\";color:\"{{foreground-1}}\"}md-toolbar.md-THEME_NAME-theme.md-menu-toolbar md-toolbar-filler{background-color:\"{{primary-color}}\";color:\"{{primary-contrast}}\"}md-toolbar.md-THEME_NAME-theme.md-menu-toolbar md-toolbar-filler md-icon{color:\"{{primary-contrast}}\"}md-nav-bar.md-THEME_NAME-theme .md-nav-bar{background-color:transparent;border-color:\"{{foreground-4}}\"}md-nav-bar.md-THEME_NAME-theme .md-button._md-nav-button.md-unselected{color:\"{{foreground-2}}\"}md-nav-bar.md-THEME_NAME-theme .md-button._md-nav-button[disabled]{color:\"{{foreground-3}}\"}md-nav-bar.md-THEME_NAME-theme md-nav-ink-bar{color:\"{{accent-color}}\";background:\"{{accent-color}}\"}md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar{background-color:\"{{accent-color}}\"}md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar .md-button._md-nav-button{color:\"{{accent-A100}}\"}md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar .md-button._md-nav-button.md-active,md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{accent-contrast}}\"}md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{accent-contrast-0.1}}\"}md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar md-nav-ink-bar{color:\"{{primary-600-1}}\";background:\"{{primary-600-1}}\"}md-nav-bar.md-THEME_NAME-theme.md-warn>.md-nav-bar{background-color:\"{{warn-color}}\"}md-nav-bar.md-THEME_NAME-theme.md-warn>.md-nav-bar .md-button._md-nav-button{color:\"{{warn-100}}\"}md-nav-bar.md-THEME_NAME-theme.md-warn>.md-nav-bar .md-button._md-nav-button.md-active,md-nav-bar.md-THEME_NAME-theme.md-warn>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{warn-contrast}}\"}md-nav-bar.md-THEME_NAME-theme.md-warn>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{warn-contrast-0.1}}\"}md-nav-bar.md-THEME_NAME-theme.md-primary>.md-nav-bar{background-color:\"{{primary-color}}\"}md-nav-bar.md-THEME_NAME-theme.md-primary>.md-nav-bar .md-button._md-nav-button{color:\"{{primary-100}}\"}md-nav-bar.md-THEME_NAME-theme.md-primary>.md-nav-bar .md-button._md-nav-button.md-active,md-nav-bar.md-THEME_NAME-theme.md-primary>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{primary-contrast}}\"}md-nav-bar.md-THEME_NAME-theme.md-primary>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{primary-contrast-0.1}}\"}md-toolbar>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar{background-color:\"{{primary-color}}\"}md-toolbar>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button{color:\"{{primary-100}}\"}md-toolbar>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-active,md-toolbar>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{primary-contrast}}\"}md-toolbar>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{primary-contrast-0.1}}\"}md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar{background-color:\"{{accent-color}}\"}md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button{color:\"{{accent-A100}}\"}md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-active,md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{accent-contrast}}\"}md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{accent-contrast-0.1}}\"}md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar md-nav-ink-bar{color:\"{{primary-600-1}}\";background:\"{{primary-600-1}}\"}md-toolbar.md-warn>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar{background-color:\"{{warn-color}}\"}md-toolbar.md-warn>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button{color:\"{{warn-100}}\"}md-toolbar.md-warn>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-active,md-toolbar.md-warn>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{warn-contrast}}\"}md-toolbar.md-warn>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{warn-contrast-0.1}}\"}._md-panel-backdrop.md-THEME_NAME-theme{background-color:\"{{background-900-1.0}}\"}md-progress-circular.md-THEME_NAME-theme path{stroke:\"{{primary-color}}\"}md-progress-circular.md-THEME_NAME-theme.md-warn path{stroke:\"{{warn-color}}\"}md-progress-circular.md-THEME_NAME-theme.md-accent path{stroke:\"{{accent-color}}\"}md-progress-linear.md-THEME_NAME-theme .md-container{background-color:\"{{primary-100}}\"}md-progress-linear.md-THEME_NAME-theme .md-bar{background-color:\"{{primary-color}}\"}md-progress-linear.md-THEME_NAME-theme.md-warn .md-container{background-color:\"{{warn-100}}\"}md-progress-linear.md-THEME_NAME-theme.md-warn .md-bar{background-color:\"{{warn-color}}\"}md-progress-linear.md-THEME_NAME-theme.md-accent .md-container{background-color:\"{{accent-100}}\"}md-progress-linear.md-THEME_NAME-theme.md-accent .md-bar{background-color:\"{{accent-color}}\"}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-primary .md-bar1{background-color:\"{{primary-100}}\"}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-primary .md-dashed:before{background:radial-gradient(\"{{primary-100}}\" 0,\"{{primary-100}}\" 16%,transparent 42%)}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-warn .md-bar1{background-color:\"{{warn-100}}\"}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-warn .md-dashed:before{background:radial-gradient(\"{{warn-100}}\" 0,\"{{warn-100}}\" 16%,transparent 42%)}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-accent .md-bar1{background-color:\"{{accent-100}}\"}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-accent .md-dashed:before{background:radial-gradient(\"{{accent-100}}\" 0,\"{{accent-100}}\" 16%,transparent 42%)}md-radio-button.md-THEME_NAME-theme .md-off{border-color:\"{{foreground-2}}\"}md-radio-button.md-THEME_NAME-theme .md-on{background-color:\"{{accent-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme.md-checked .md-off{border-color:\"{{accent-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme.md-checked .md-ink-ripple{color:\"{{accent-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme .md-container .md-ripple{color:\"{{accent-A700}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary .md-on,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary .md-on,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary .md-on,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary .md-on{background-color:\"{{primary-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary.md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary .md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary .md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary.md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary .md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary .md-checked .md-off{border-color:\"{{primary-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary.md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary .md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary .md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary.md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary .md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary .md-checked .md-ink-ripple{color:\"{{primary-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary .md-container .md-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary .md-container .md-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary .md-container .md-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary .md-container .md-ripple{color:\"{{primary-600}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn .md-on,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn .md-on,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn .md-on,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn .md-on{background-color:\"{{warn-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn.md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn .md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn .md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn.md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn .md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn .md-checked .md-off{border-color:\"{{warn-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn.md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn .md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn .md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn.md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn .md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn .md-checked .md-ink-ripple{color:\"{{warn-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn .md-container .md-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn .md-container .md-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn .md-container .md-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn .md-container .md-ripple{color:\"{{warn-600}}\"}md-radio-button.md-THEME_NAME-theme[disabled],md-radio-group.md-THEME_NAME-theme[disabled]{color:\"{{foreground-3}}\"}md-radio-button.md-THEME_NAME-theme[disabled] .md-container .md-off,md-radio-button.md-THEME_NAME-theme[disabled] .md-container .md-on,md-radio-group.md-THEME_NAME-theme[disabled] .md-container .md-off,md-radio-group.md-THEME_NAME-theme[disabled] .md-container .md-on{border-color:\"{{foreground-3}}\"}md-radio-group.md-THEME_NAME-theme .md-checked .md-ink-ripple{color:\"{{accent-color-0.26}}\"}md-radio-group.md-THEME_NAME-theme .md-checked:not([disabled]).md-primary .md-ink-ripple,md-radio-group.md-THEME_NAME-theme.md-primary .md-checked:not([disabled]) .md-ink-ripple{color:\"{{primary-color-0.26}}\"}md-radio-group.md-THEME_NAME-theme.md-focused.ng-empty>md-radio-button:first-child .md-container:before{background-color:\"{{foreground-3-0.26}}\"}md-radio-group.md-THEME_NAME-theme.md-focused:not(:empty) .md-checked .md-container:before{background-color:\"{{accent-color-0.26}}\"}md-radio-group.md-THEME_NAME-theme.md-focused:not(:empty) .md-checked.md-primary .md-container:before,md-radio-group.md-THEME_NAME-theme.md-focused:not(:empty).md-primary .md-checked .md-container:before{background-color:\"{{primary-color-0.26}}\"}md-radio-group.md-THEME_NAME-theme.md-focused:not(:empty) .md-checked.md-warn .md-container:before,md-radio-group.md-THEME_NAME-theme.md-focused:not(:empty).md-warn .md-checked .md-container:before{background-color:\"{{warn-color-0.26}}\"}md-input-container md-select.md-THEME_NAME-theme .md-select-value span:first-child:after{color:\"{{warn-A700}}\"}md-input-container:not(.md-input-focused):not(.md-input-invalid) md-select.md-THEME_NAME-theme .md-select-value span:first-child:after{color:\"{{foreground-3}}\"}md-input-container.md-input-focused:not(.md-input-has-value) md-select.md-THEME_NAME-theme .md-select-value,md-input-container.md-input-focused:not(.md-input-has-value) md-select.md-THEME_NAME-theme .md-select-value.md-select-placeholder{color:\"{{primary-color}}\"}md-input-container.md-input-invalid md-select.md-THEME_NAME-theme .md-select-value{color:\"{{warn-A700}}\"!important;border-bottom-color:\"{{warn-A700}}\"!important}md-input-container.md-input-invalid md-select.md-THEME_NAME-theme.md-no-underline .md-select-value{border-bottom-color:transparent!important}md-input-container:not(.md-input-invalid).md-input-focused.md-accent .md-select-value{border-color:\"{{accent-color}}\"}md-input-container:not(.md-input-invalid).md-input-focused.md-accent .md-select-value span{color:\"{{accent-color}}\"}md-input-container:not(.md-input-invalid).md-input-focused.md-warn .md-select-value{border-color:\"{{warn-A700}}\"}md-input-container:not(.md-input-invalid).md-input-focused.md-warn .md-select-value span{color:\"{{warn-A700}}\"}md-select.md-THEME_NAME-theme[disabled] .md-select-value{border-bottom-color:transparent;background-image:linear-gradient(90deg,\"{{foreground-3}}\" 0,\"{{foreground-3}}\" 33%,transparent 0);background-image:-ms-linear-gradient(left,transparent 0,\"{{foreground-3}}\" 100%)}md-select.md-THEME_NAME-theme .md-select-value{border-bottom-color:\"{{foreground-4}}\"}md-select.md-THEME_NAME-theme .md-select-value.md-select-placeholder{color:\"{{foreground-3}}\"}md-select.md-THEME_NAME-theme .md-select-value span:first-child:after{color:\"{{warn-A700}}\"}md-select.md-THEME_NAME-theme.md-no-underline .md-select-value{border-bottom-color:transparent!important}md-select.md-THEME_NAME-theme.ng-invalid.ng-touched .md-select-value{color:\"{{warn-A700}}\"!important;border-bottom-color:\"{{warn-A700}}\"!important}md-select.md-THEME_NAME-theme.ng-invalid.ng-touched.md-no-underline .md-select-value{border-bottom-color:transparent!important}md-select.md-THEME_NAME-theme:not([disabled]):focus .md-select-value{border-bottom-color:\"{{primary-color}}\";color:\"{{ foreground-1 }}\"}md-select.md-THEME_NAME-theme:not([disabled]):focus .md-select-value.md-select-placeholder{color:\"{{ foreground-1 }}\"}md-select.md-THEME_NAME-theme:not([disabled]):focus.md-no-underline .md-select-value{border-bottom-color:transparent!important}md-select.md-THEME_NAME-theme:not([disabled]):focus.md-accent .md-select-value{border-bottom-color:\"{{accent-color}}\"}md-select.md-THEME_NAME-theme:not([disabled]):focus.md-warn .md-select-value{border-bottom-color:\"{{warn-color}}\"}md-select.md-THEME_NAME-theme[disabled] .md-select-icon,md-select.md-THEME_NAME-theme[disabled] .md-select-value,md-select.md-THEME_NAME-theme[disabled] .md-select-value.md-select-placeholder{color:\"{{foreground-3}}\"}md-select.md-THEME_NAME-theme .md-select-icon{color:\"{{foreground-2}}\"}md-select-menu.md-THEME_NAME-theme md-content{background-color:\"{{background-hue-1}}\"}md-select-menu.md-THEME_NAME-theme md-content md-optgroup{color:\"{{foreground-2}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option{color:\"{{foreground-1}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option[disabled] .md-text{color:\"{{foreground-3}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option:not([disabled]).md-focused,md-select-menu.md-THEME_NAME-theme md-content md-option:not([disabled]):focus,md-select-menu.md-THEME_NAME-theme md-content md-option:not([disabled]):hover{background-color:\"{{background-500-0.18}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option[selected]{color:\"{{primary-500}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option[selected].md-focused,md-select-menu.md-THEME_NAME-theme md-content md-option[selected]:focus{color:\"{{primary-600}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option[selected].md-accent{color:\"{{accent-color}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option[selected].md-accent.md-focused,md-select-menu.md-THEME_NAME-theme md-content md-option[selected].md-accent:focus{color:\"{{accent-A700}}\"}.md-checkbox-enabled.md-THEME_NAME-theme .md-ripple{color:\"{{primary-600}}\"}.md-checkbox-enabled.md-THEME_NAME-theme[selected] .md-ripple{color:\"{{background-600}}\"}.md-checkbox-enabled.md-THEME_NAME-theme .md-ink-ripple{color:\"{{foreground-2}}\"}.md-checkbox-enabled.md-THEME_NAME-theme[selected] .md-ink-ripple{color:\"{{primary-color-0.87}}\"}.md-checkbox-enabled.md-THEME_NAME-theme:not(.md-checked) .md-icon{border-color:\"{{foreground-2}}\"}.md-checkbox-enabled.md-THEME_NAME-theme[selected] .md-icon{background-color:\"{{primary-color-0.87}}\"}.md-checkbox-enabled.md-THEME_NAME-theme[selected].md-focused .md-container:before{background-color:\"{{primary-color-0.26}}\"}.md-checkbox-enabled.md-THEME_NAME-theme[selected] .md-icon:after{border-color:\"{{primary-contrast-0.87}}\"}.md-checkbox-enabled.md-THEME_NAME-theme .md-indeterminate[disabled] .md-container{color:\"{{foreground-3}}\"}.md-checkbox-enabled.md-THEME_NAME-theme md-option .md-text{color:\"{{foreground-1}}\"}md-sidenav.md-THEME_NAME-theme,md-sidenav.md-THEME_NAME-theme md-content{background-color:\"{{background-hue-1}}\"}md-slider.md-THEME_NAME-theme .md-track{background-color:\"{{foreground-3}}\"}md-slider.md-THEME_NAME-theme .md-track-ticks{color:\"{{background-contrast}}\"}md-slider.md-THEME_NAME-theme .md-focus-ring{background-color:\"{{accent-A200-0.2}}\"}md-slider.md-THEME_NAME-theme .md-disabled-thumb{border-color:\"{{background-color}}\";background-color:\"{{background-color}}\"}md-slider.md-THEME_NAME-theme.md-min .md-thumb:after{background-color:\"{{background-color}}\";border-color:\"{{foreground-3}}\"}md-slider.md-THEME_NAME-theme.md-min .md-focus-ring{background-color:\"{{foreground-3-0.38}}\"}md-slider.md-THEME_NAME-theme.md-min[md-discrete] .md-thumb:after{background-color:\"{{background-contrast}}\";border-color:transparent}md-slider.md-THEME_NAME-theme.md-min[md-discrete] .md-sign{background-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme.md-min[md-discrete] .md-sign:after{border-top-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme.md-min[md-discrete][md-vertical] .md-sign:after{border-top-color:transparent;border-left-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme .md-track.md-track-fill{background-color:\"{{accent-color}}\"}md-slider.md-THEME_NAME-theme .md-thumb:after{border-color:\"{{accent-color}}\";background-color:\"{{accent-color}}\"}md-slider.md-THEME_NAME-theme .md-sign{background-color:\"{{accent-color}}\"}md-slider.md-THEME_NAME-theme .md-sign:after{border-top-color:\"{{accent-color}}\"}md-slider.md-THEME_NAME-theme[md-vertical] .md-sign:after{border-top-color:transparent;border-left-color:\"{{accent-color}}\"}md-slider.md-THEME_NAME-theme .md-thumb-text{color:\"{{accent-contrast}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-focus-ring{background-color:\"{{warn-200-0.38}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-track.md-track-fill{background-color:\"{{warn-color}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-thumb:after{border-color:\"{{warn-color}}\";background-color:\"{{warn-color}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-sign{background-color:\"{{warn-color}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-sign:after{border-top-color:\"{{warn-color}}\"}md-slider.md-THEME_NAME-theme.md-warn[md-vertical] .md-sign:after{border-top-color:transparent;border-left-color:\"{{warn-color}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-thumb-text{color:\"{{warn-contrast}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-focus-ring{background-color:\"{{primary-200-0.38}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-track.md-track-fill{background-color:\"{{primary-color}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-thumb:after{border-color:\"{{primary-color}}\";background-color:\"{{primary-color}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-sign{background-color:\"{{primary-color}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-sign:after{border-top-color:\"{{primary-color}}\"}md-slider.md-THEME_NAME-theme.md-primary[md-vertical] .md-sign:after{border-top-color:transparent;border-left-color:\"{{primary-color}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-thumb-text{color:\"{{primary-contrast}}\"}md-slider.md-THEME_NAME-theme[disabled] .md-thumb:after{border-color:transparent}md-slider.md-THEME_NAME-theme[disabled]:not(.md-min) .md-thumb:after,md-slider.md-THEME_NAME-theme[disabled][md-discrete] .md-thumb:after{background-color:\"{{foreground-3}}\";border-color:transparent}md-slider.md-THEME_NAME-theme[disabled][readonly] .md-sign{background-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme[disabled][readonly] .md-sign:after{border-top-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme[disabled][readonly][md-vertical] .md-sign:after{border-top-color:transparent;border-left-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme[disabled][readonly] .md-disabled-thumb{border-color:transparent;background-color:transparent}md-slider-container[disabled]>:first-child:not(md-slider),md-slider-container[disabled]>:last-child:not(md-slider){color:\"{{foreground-3}}\"}.md-subheader.md-THEME_NAME-theme{color:\"{{ foreground-2-0.23 }}\";background-color:\"{{background-default}}\"}.md-subheader.md-THEME_NAME-theme.md-primary{color:\"{{primary-color}}\"}.md-subheader.md-THEME_NAME-theme.md-accent{color:\"{{accent-color}}\"}.md-subheader.md-THEME_NAME-theme.md-warn{color:\"{{warn-color}}\"}md-switch.md-THEME_NAME-theme .md-ink-ripple{color:\"{{background-500}}\"}md-switch.md-THEME_NAME-theme .md-thumb{background-color:\"{{background-50}}\"}md-switch.md-THEME_NAME-theme .md-bar{background-color:\"{{background-500}}\"}md-switch.md-THEME_NAME-theme.md-focused:not(.md-checked) .md-thumb:before,md-switch.md-THEME_NAME-theme.md-focused[disabled] .md-thumb:before{background-color:\"{{foreground-4}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]) .md-ink-ripple{color:\"{{accent-color}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]) .md-thumb{background-color:\"{{accent-color}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]) .md-bar{background-color:\"{{accent-color-0.5}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]).md-focused .md-thumb:before{background-color:\"{{accent-color-0.26}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]).md-primary .md-ink-ripple{color:\"{{primary-color}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]).md-primary .md-thumb{background-color:\"{{primary-color}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]).md-primary .md-bar{background-color:\"{{primary-color-0.5}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]).md-primary.md-focused .md-thumb:before{background-color:\"{{primary-color-0.26}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]).md-warn .md-ink-ripple{color:\"{{warn-color}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]).md-warn .md-thumb{background-color:\"{{warn-color}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]).md-warn .md-bar{background-color:\"{{warn-color-0.5}}\"}md-switch.md-THEME_NAME-theme.md-checked:not([disabled]).md-warn.md-focused .md-thumb:before{background-color:\"{{warn-color-0.26}}\"}md-switch.md-THEME_NAME-theme[disabled] .md-thumb{background-color:\"{{background-400}}\"}md-switch.md-THEME_NAME-theme[disabled] .md-bar{background-color:\"{{foreground-4}}\"}md-tabs.md-THEME_NAME-theme md-tabs-wrapper{background-color:transparent;border-color:\"{{foreground-4}}\"}md-tabs.md-THEME_NAME-theme .md-paginator md-icon{color:\"{{primary-color}}\"}md-tabs.md-THEME_NAME-theme md-ink-bar{color:\"{{accent-color}}\";background:\"{{accent-color}}\"}md-tabs.md-THEME_NAME-theme .md-tab{color:\"{{foreground-2}}\"}md-tabs.md-THEME_NAME-theme .md-tab[disabled],md-tabs.md-THEME_NAME-theme .md-tab[disabled] md-icon{color:\"{{foreground-3}}\"}md-tabs.md-THEME_NAME-theme .md-tab.md-active,md-tabs.md-THEME_NAME-theme .md-tab.md-active md-icon,md-tabs.md-THEME_NAME-theme .md-tab.md-focused,md-tabs.md-THEME_NAME-theme .md-tab.md-focused md-icon{color:\"{{primary-color}}\"}md-tabs.md-THEME_NAME-theme .md-tab.md-focused{background:\"{{primary-color-0.1}}\"}md-tabs.md-THEME_NAME-theme .md-tab .md-ripple-container{color:\"{{accent-A100}}\"}md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper{background-color:\"{{accent-color}}\"}md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{accent-A100}}\"}md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{accent-contrast}}\"}md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{accent-contrast-0.1}}\"}md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-ink-bar{color:\"{{primary-600-1}}\";background:\"{{primary-600-1}}\"}md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper{background-color:\"{{primary-color}}\"}md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{primary-100}}\"}md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{primary-contrast}}\"}md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{primary-contrast-0.1}}\"}md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper{background-color:\"{{warn-color}}\"}md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{warn-100}}\"}md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{warn-contrast}}\"}md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{warn-contrast-0.1}}\"}md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper{background-color:\"{{primary-color}}\"}md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{primary-100}}\"}md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{primary-contrast}}\"}md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{primary-contrast-0.1}}\"}md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper{background-color:\"{{accent-color}}\"}md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{accent-A100}}\"}md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{accent-contrast}}\"}md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{accent-contrast-0.1}}\"}md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-ink-bar{color:\"{{primary-600-1}}\";background:\"{{primary-600-1}}\"}md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper{background-color:\"{{warn-color}}\"}md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{warn-100}}\"}md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{warn-contrast}}\"}md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{warn-contrast-0.1}}\"}md-toast.md-THEME_NAME-theme .md-toast-content{background-color:#323232;color:\"{{background-50}}\"}md-toast.md-THEME_NAME-theme .md-toast-content .md-button{color:\"{{background-50}}\"}md-toast.md-THEME_NAME-theme .md-toast-content .md-button.md-highlight{color:\"{{accent-color}}\"}md-toast.md-THEME_NAME-theme .md-toast-content .md-button.md-highlight.md-primary{color:\"{{primary-color}}\"}md-toast.md-THEME_NAME-theme .md-toast-content .md-button.md-highlight.md-warn{color:\"{{warn-color}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar){background-color:\"{{primary-color}}\";color:\"{{primary-contrast}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar) md-icon{color:\"{{primary-contrast}}\";fill:\"{{primary-contrast}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar) .md-button[disabled] md-icon{color:\"{{primary-contrast-0.26}}\";fill:\"{{primary-contrast-0.26}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar).md-accent{background-color:\"{{accent-color}}\";color:\"{{accent-contrast}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar).md-accent .md-ink-ripple{color:\"{{accent-contrast}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar).md-accent md-icon{color:\"{{accent-contrast}}\";fill:\"{{accent-contrast}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar).md-accent .md-button[disabled] md-icon{color:\"{{accent-contrast-0.26}}\";fill:\"{{accent-contrast-0.26}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar).md-warn{background-color:\"{{warn-color}}\";color:\"{{warn-contrast}}\"}.md-panel.md-tooltip.md-THEME_NAME-theme{color:\"{{background-700-contrast}}\";background-color:\"{{background-700}}\"}body.md-THEME_NAME-theme,html.md-THEME_NAME-theme{color:\"{{foreground-1}}\";background-color:\"{{background-color}}\"}"); 
})();


})(window, window.angular);;window.ngMaterial={version:{full: "1.1.24"}};
/*
 * Angular Material Data Table
 * https://github.com/daniel-nagy/md-data-table
 * @license MIT
 * v0.10.9
 */
(function (window, angular, undefined) {
'use strict';

angular.module('md.table.templates', ['md-table-pagination.html', 'md-table-progress.html', 'arrow-up.svg', 'navigate-before.svg', 'navigate-first.svg', 'navigate-last.svg', 'navigate-next.svg']);

angular.module('md-table-pagination.html', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('md-table-pagination.html',
    '<div class="page-select" ng-if="$pagination.showPageSelect()">\n' +
    '  <div class="label">{{$pagination.label.page}}</div>\n' +
    '\n' +
    '  <md-select virtual-page-select total="{{$pagination.pages()}}" class="md-table-select" ng-model="$pagination.page" md-container-class="md-pagination-select" ng-change="$pagination.onPaginationChange()" ng-disabled="$pagination.disabled" aria-label="Page">\n' +
    '    <md-content>\n' +
    '      <md-option ng-repeat="page in $pageSelect.pages" ng-value="page">{{page}}</md-option>\n' +
    '    </md-content>\n' +
    '  </md-select>\n' +
    '</div>\n' +
    '\n' +
    '<div class="limit-select" ng-if="$pagination.limitOptions">\n' +
    '  <div class="label">{{$pagination.label.rowsPerPage}}</div>\n' +
    '\n' +
    '  <md-select class="md-table-select" ng-model="$pagination.limit" md-container-class="md-pagination-select" ng-disabled="$pagination.disabled" aria-label="Rows" placeholder="{{ $pagination.limitOptions[0] }}">\n' +
    '    <md-option ng-repeat="option in $pagination.limitOptions" ng-value="option.value ? $pagination.eval(option.value) : option">{{::option.label ? option.label : option}}</md-option>\n' +
    '  </md-select>\n' +
    '</div>\n' +
    '\n' +
    '<div class="buttons">\n' +
    '  <div class="label">{{$pagination.min()}} - {{$pagination.max()}} {{$pagination.label.of}} {{$pagination.total}}</div>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-if="$pagination.showBoundaryLinks()" ng-click="$pagination.first()" ng-disabled="$pagination.disabled || !$pagination.hasPrevious()" aria-label="First">\n' +
    '    <md-icon md-svg-icon="navigate-first.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-click="$pagination.previous()" ng-disabled="$pagination.disabled || !$pagination.hasPrevious()" aria-label="Previous">\n' +
    '    <md-icon md-svg-icon="navigate-before.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-click="$pagination.next()" ng-disabled="$pagination.disabled || !$pagination.hasNext()" aria-label="Next">\n' +
    '    <md-icon md-svg-icon="navigate-next.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-if="$pagination.showBoundaryLinks()" ng-click="$pagination.last()" ng-disabled="$pagination.disabled || !$pagination.hasNext()" aria-label="Last">\n' +
    '    <md-icon md-svg-icon="navigate-last.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '</div>');
}]);

angular.module('md-table-progress.html', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('md-table-progress.html',
    '<tr>\n' +
    '  <th colspan="{{columnCount()}}">\n' +
    '    <md-progress-linear ng-show="deferred()" md-mode="indeterminate"></md-progress-linear>\n' +
    '  </th>\n' +
    '</tr>');
}]);

angular.module('arrow-up.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('arrow-up.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"/></svg>');
}]);

angular.module('navigate-before.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-before.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>');
}]);

angular.module('navigate-first.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-first.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M7 6 v12 h2 v-12 h-2z M17.41 7.41L16 6l-6 6 6 6 1.41-1.41L12.83 12z"/></svg>');
}]);

angular.module('navigate-last.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-last.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M15 6 v12 h2 v-12 h-2z M8 6L6.59 7.41 11.17 12l-4.58 4.59L8 18l6-6z"/></svg>');
}]);

angular.module('navigate-next.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-next.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>');
}]);


angular.module('md.data.table', ['md.table.templates']);

angular.module('md.data.table').directive('mdBody', mdBody);

function mdBody() {

  function compile(tElement) {
    tElement.addClass('md-body');
  }

  return {
    compile: compile,
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdCell', mdCell);

function mdCell() {
  
  function compile(tElement) {
    var select = tElement.find('md-select');
    
    if(select.length) {
      select.addClass('md-table-select').attr('md-container-class', 'md-table-select');
    }
    
    tElement.addClass('md-cell');
    
    return postLink;
  }
  
  // empty controller to be bind properties to in postLink function
  function Controller() {
    
  }
  
  function postLink(scope, element, attrs, ctrls) {
    var select = element.find('md-select');
    var cellCtrl = ctrls.shift();
    var tableCtrl = ctrls.shift();
    
    if(attrs.ngClick) {
      element.addClass('md-clickable');
    }
    
    if(select.length) {
      select.on('click', function (event) {
        event.stopPropagation();
      });
      
      element.addClass('md-clickable').on('click', function (event) {
        event.stopPropagation();
        select[0].click();
      });
    }
    
    cellCtrl.getTable = tableCtrl.getElement;
    
    function getColumn() {
      return tableCtrl.$$columns[getIndex()];
    }
    
    function getIndex() {
      return Array.prototype.indexOf.call(element.parent().children(), element[0]);
    }
    
    scope.$watch(getColumn, function (column) {
      if(!column) {
        return;
      }
      
      if(column.numeric) {
        element.addClass('md-numeric');
      } else {
        element.removeClass('md-numeric');
      }
    });
  }
  
  return {
    controller: Controller,
    compile: compile,
    require: ['mdCell', '^^mdTable'],
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdColumn', mdColumn);

function mdColumn($compile, $mdUtil) {

  function compile(tElement) {
    tElement.addClass('md-column');
    return postLink;
  }

  function postLink(scope, element, attrs, ctrls) {
    var headCtrl = ctrls.shift();
    var tableCtrl = ctrls.shift();

    function attachSortIcon() {
      var sortIcon = angular.element('<md-icon md-svg-icon="arrow-up.svg">');

      $compile(sortIcon.addClass('md-sort-icon').attr('ng-class', 'getDirection()'))(scope);

      if(element.hasClass('md-numeric')) {
        element.prepend(sortIcon);
      } else {
        element.append(sortIcon);
      }
    }

    function detachSortIcon() {
      Array.prototype.some.call(element.find('md-icon'), function (icon) {
        return icon.classList.contains('md-sort-icon') && element[0].removeChild(icon);
      });
    }

    function disableSorting() {
      detachSortIcon();
      element.removeClass('md-sort').off('click', setOrder);
    }

    function enableSorting() {
      attachSortIcon();
      element.addClass('md-sort').on('click', setOrder);
    }

    function getIndex() {
      return Array.prototype.indexOf.call(element.parent().children(), element[0]);
    }

    function isActive() {
      return scope.orderBy && (headCtrl.order === scope.orderBy || headCtrl.order === '-' + scope.orderBy);
    }

    function isNumeric() {
      return attrs.mdNumeric === '' || scope.numeric;
    }

    function setOrder() {
      scope.$applyAsync(function () {
        if(isActive()) {
          headCtrl.order = scope.getDirection() === 'md-asc' ? '-' + scope.orderBy : scope.orderBy;
        } else {
          headCtrl.order = scope.getDirection() === 'md-asc' ? scope.orderBy : '-' + scope.orderBy;
        }

        if(angular.isFunction(headCtrl.onReorder)) {
          $mdUtil.nextTick(function () {
            headCtrl.onReorder(headCtrl.order);
          });
        }
      });
    }

    function updateColumn(index, column) {
      tableCtrl.$$columns[index] = column;

      if(column.numeric) {
        element.addClass('md-numeric');
      } else {
        element.removeClass('md-numeric');
      }
    }

    scope.getDirection = function () {
      if(isActive()) {
        return headCtrl.order.charAt(0) === '-' ? 'md-desc' : 'md-asc';
      }

      return attrs.mdDesc === '' || scope.$eval(attrs.mdDesc) ? 'md-desc' : 'md-asc';
    };

    scope.$watch(isActive, function (active) {
      if(active) {
        element.addClass('md-active');
      } else {
        element.removeClass('md-active');
      }
    });

    scope.$watch(getIndex, function (index) {
      updateColumn(index, {'numeric': isNumeric()});
    });

    scope.$watch(isNumeric, function (numeric) {
      updateColumn(getIndex(), {'numeric': numeric});
    });

    scope.$watch('orderBy', function (orderBy) {
      if(orderBy) {
        if(!element.hasClass('md-sort')) {
          enableSorting();
        }
      } else if(element.hasClass('md-sort')) {
        disableSorting();
      }
    });
  }

  return {
    compile: compile,
    require: ['^^mdHead', '^^mdTable'],
    restrict: 'A',
    scope: {
      numeric: '=?mdNumeric',
      orderBy: '@?mdOrderBy'
    }
  };
}

mdColumn.$inject = ['$compile', '$mdUtil'];

angular.module('md.data.table')
  .decorator('$controller', controllerDecorator)
  .factory('$mdEditDialog', mdEditDialog);

/*
 * A decorator for ng.$controller to optionally bind properties to the
 * controller before invoking the constructor. Stolen from the ngMock.
 *
 * https://docs.angularjs.org/api/ngMock/service/$controller
 */
function controllerDecorator($delegate) {
  return function(expression, locals, later, ident) {
    if(later && typeof later === 'object') {
      var create = $delegate(expression, locals, true, ident);
      angular.extend(create.instance, later);
      return create();
    }
    return $delegate(expression, locals, later, ident);
  };
}

controllerDecorator.$inject = ['$delegate'];
  
function mdEditDialog($compile, $controller, $document, $mdUtil, $q, $rootScope, $templateCache, $templateRequest, $window) {
  /* jshint validthis: true */
  
  var ESCAPE = 27;
  
  var busy = false;
  var body = angular.element($document.prop('body'));
  
  /*
   * bindToController
   * controller
   * controllerAs
   * locals
   * resolve
   * scope
   * targetEvent
   * template
   * templateUrl
   */
  var defaultOptions = {
    clickOutsideToClose: true,
    disableScroll: true,
    escToClose: true,
    focusOnOpen: true
  };
  
  function build(template, options) {
    var scope = $rootScope.$new();
    var element = $compile(template)(scope);
    var backdrop = $mdUtil.createBackdrop(scope, 'md-edit-dialog-backdrop');
    var controller;
    
    if(options.controller) {
      controller = getController(options, scope, {$element: element, $scope: scope});
    } else {
      angular.extend(scope, options.scope);
    }
    
    if(options.disableScroll) {
      disableScroll(element);
    }
    
    body.prepend(backdrop).append(element.addClass('md-whiteframe-1dp'));
    
    positionDialog(element, options.target);
    
    if(options.focusOnOpen) {
      focusOnOpen(element);
    }
    
    if(options.clickOutsideToClose) {
      backdrop.on('click', function () {
        element.remove();
      });
    }
    
    if(options.escToClose) {
      escToClose(element);
    }
    
    element.on('$destroy', function () {
      busy = false;
      backdrop.remove();
    });
    
    return controller;
  }
  
  function disableScroll(element) {
    var restoreScroll = $mdUtil.disableScrollAround(element, body);
    
    element.on('$destroy', function () {
      restoreScroll();
    });
  }
  
  function getController(options, scope, inject) {
    if(!options.controller) {
      return;
    }
    
    if(options.resolve) {
      angular.extend(inject, options.resolve);
    }
    
    if(options.locals) {
      angular.extend(inject, options.locals);
    }
    
    if(options.controllerAs) {
      scope[options.controllerAs] = {};
      
      if(options.bindToController) {
        angular.extend(scope[options.controllerAs], options.scope);
      } else {
        angular.extend(scope, options.scope);
      }
    } else {
      angular.extend(scope, options.scope);
    }
    
    if(options.bindToController) {
      return $controller(options.controller, inject, scope[options.controllerAs]);
    } else {
      return $controller(options.controller, inject);
    }
  }
  
  function getTemplate(options) {
    return $q(function (resolve, reject) {
      var template = options.template;
      
      function illegalType(type) {
        reject('Unexpected template value. Expected a string; received a ' + type + '.');
      }
      
      if(template) {
        return angular.isString(template) ? resolve(template) : illegalType(typeof template);
      }
      
      if(options.templateUrl) {
        template = $templateCache.get(options.templateUrl);
        
        if(template) {
          return resolve(template);
        }
        
        var success = function (template) {
          return resolve(template);
        };
        
        var error = function () {
          return reject('Error retrieving template from URL.');
        };
        
        return $templateRequest(options.templateUrl).then(success, error);
      }
      
      reject('Template not provided.');
    });
  }
  
  function logError(error) {
    busy = false;
    console.error(error);
  }
  
  function escToClose(element) {
    var keyup = function (event) {
      if(event.keyCode === ESCAPE) {
        element.remove();
      }
    };
    
    body.on('keyup', keyup);
    
    element.on('$destroy', function () {
      body.off('keyup', keyup);
    });
  }

  function focusOnOpen(element) {
    $mdUtil.nextTick(function () {
      var autofocus = $mdUtil.findFocusTarget(element);
      
      if(autofocus) {
        autofocus.focus();
      }
    }, false);
  }

  function positionDialog(element, target) {
    var table = angular.element(target).controller('mdCell').getTable();
    
    var getHeight = function () {
      return element.prop('clientHeight');
    };
    
    var getSize = function () {
      return {
        width: getWidth(),
        height: getHeight()
      };
    };
    
    var getTableBounds = function () {
      var parent = table.parent();
      
      if(parent.prop('tagName') === 'MD-TABLE-CONTAINER') {
        return parent[0].getBoundingClientRect();
      } else {
        return table[0].getBoundingClientRect();
      }
    };
    
    var getWidth = function () {
      return element.prop('clientWidth');
    };
    
    var reposition = function () {
      var size = getSize();
      var cellBounds = target.getBoundingClientRect();
      var tableBounds = getTableBounds();
      
      if(size.width > tableBounds.right - cellBounds.left) {
        element.css('left', tableBounds.right - size.width + 'px');
      } else {
        element.css('left', cellBounds.left + 'px');
      }
      
      if(size.height > tableBounds.bottom - cellBounds.top) {
        element.css('top', tableBounds.bottom - size.height + 'px');
      } else {
        element.css('top', cellBounds.top + 1 + 'px');
      }
      
      element.css('minWidth', cellBounds.width + 'px');
    };
    
    var watchWidth = $rootScope.$watch(getWidth, reposition);
    var watchHeight = $rootScope.$watch(getHeight, reposition);
    
    $window.addEventListener('resize', reposition);
    
    element.on('$destroy', function () {
      watchWidth();
      watchHeight();
      
      $window.removeEventListener('resize', reposition);
    });
  }
  
  function preset(size, options) {
    
    function getAttrs() {
      var attrs = 'type="' + (options.type || 'text') + '"';
      
      for(var attr in options.validators) {
        attrs += ' ' + attr + '="' + options.validators[attr] + '"';
      }
      
      return attrs;
    }
    
    return {
      controller: ['$element', '$q', 'save', '$scope', function ($element, $q, save, $scope) {
        function update() {
          if($scope.editDialog.$invalid) {
            return $q.reject();
          }
          
          if(angular.isFunction(save)) {
            return $q.when(save($scope.editDialog.input));
          }
          
          return $q.resolve();
        }
        
        this.dismiss = function () {
          $element.remove();
        };
        
        this.getInput = function () {
          return $scope.editDialog.input;
        };
        
        $scope.dismiss = this.dismiss;
        
        $scope.submit = function () {
          update().then(function () {
            $scope.dismiss();
          });
        };
      }],
      locals: {
        save: options.save
      },
      scope: {
        cancel: options.cancel || 'Cancel',
        messages: options.messages,
        model: options.modelValue,
        ok: options.ok || 'Save',
        placeholder: options.placeholder,
        title: options.title,
        size: size
      },
      template:
        '<md-edit-dialog>' +
          '<div layout="column" class="md-content">' +
            '<div ng-if="size === \'large\'" class="md-title">{{title || \'Edit\'}}</div>' +
            '<form name="editDialog" layout="column" ng-submit="submit(model)">' +
              '<md-input-container md-no-float>' +
                '<input name="input" ng-model="model" md-autofocus placeholder="{{placeholder}} "' + getAttrs() + '>' +
                '<div ng-messages="editDialog.input.$error">' +
                  '<div ng-repeat="(key, message) in messages" ng-message="{{key}}">{{message}}</div>' +
                '</div>' +
              '</md-input-container>' +
            '</form>' +
          '</div>' +
          '<div ng-if="size === \'large\'" layout="row" layout-align="end" class="md-actions">' +
            '<md-button class="md-primary" ng-click="dismiss()">{{cancel}}</md-button>' +
            '<md-button class="md-primary" ng-click="submit()">{{ok}}</md-button>' +
          '</div>' +
        '</md-edit-dialog>'
    };
  }
  
  this.show = function (options) {
    if(busy) {
      return $q.reject();
    }
    
    busy = true;
    options = angular.extend({}, defaultOptions, options);
    
    if(!options.targetEvent) {
      return logError('options.targetEvent is required to align the dialog with the table cell.');
    }
    
    if(!options.targetEvent.currentTarget.classList.contains('md-cell')) {
      return logError('The event target must be a table cell.');
    }
    
    if(options.bindToController && !options.controllerAs) {
      return logError('You must define options.controllerAs when options.bindToController is true.');
    }
    
    options.target = options.targetEvent.currentTarget;
    
    var promise = getTemplate(options);
    var promises = [promise];
    
    for(var prop in options.resolve) {
      promise = options.resolve[prop];
      promises.push($q.when(angular.isFunction(promise) ? promise() : promise));
    }
    
    promise = $q.all(promises);
    
    promise['catch'](logError);
    
    return promise.then(function (results) {
      var template = results.shift();
      
      for(var prop in options.resolve) {
        options.resolve[prop] = results.shift();
      }
      
      return build(template, options);
    });
  };
  
  this.small = function (options) {
    return this.show(angular.extend({}, options, preset('small', options)));
  }.bind(this);
  
  this.large = function (options) {
    return this.show(angular.extend({}, options, preset('large', options)));
  }.bind(this);
  
  return this;
}

mdEditDialog.$inject = ['$compile', '$controller', '$document', '$mdUtil', '$q', '$rootScope', '$templateCache', '$templateRequest', '$window'];


angular.module('md.data.table').directive('mdFoot', mdFoot);

function mdFoot() {

  function compile(tElement) {
    tElement.addClass('md-foot');
  }

  return {
    compile: compile,
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdHead', mdHead);

function mdHead($compile) {

  function compile(tElement) {
    tElement.addClass('md-head');
    return postLink;
  }
  
  // empty controller to be bind scope properties to
  function Controller() {
    
  }
  
  function postLink(scope, element, attrs, tableCtrl) {
    // because scope.$watch is unpredictable
    var oldValue = new Array(2);
    
    function addCheckboxColumn() {
      element.children().prepend('<th class="md-column md-checkbox-column">');
    }
    
    function attatchCheckbox() {
      element.prop('lastElementChild').firstElementChild.appendChild($compile(createCheckBox())(scope)[0]);
    }
    
    function createCheckBox() {
      return angular.element('<md-checkbox>').attr({
        'aria-label': 'Select All',
        'ng-click': 'toggleAll()',
        'ng-checked': 'allSelected()',
        'ng-disabled': '!getSelectableRows().length'
      });
    }
    
    function detachCheckbox() {
      var cell = element.prop('lastElementChild').firstElementChild;
      
      if(cell.classList.contains('md-checkbox-column')) {
        angular.element(cell).empty();
      }
    }
    
    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }
    
    function mdSelectCtrl(row) {
      return angular.element(row).controller('mdSelect');
    }
    
    function removeCheckboxColumn() {
      Array.prototype.some.call(element.find('th'), function (cell) {
        return cell.classList.contains('md-checkbox-column') && cell.remove();
      });
    }
    
    scope.allSelected = function () {
      var rows = scope.getSelectableRows();
      
      return rows.length && rows.every(function (row) {
        return row.isSelected();
      });
    };
    
    scope.getSelectableRows = function () {
      return tableCtrl.getBodyRows().map(mdSelectCtrl).filter(function (ctrl) {
        return ctrl && !ctrl.disabled;
      });
    };
    
    scope.selectAll = function () {
      tableCtrl.getBodyRows().map(mdSelectCtrl).forEach(function (ctrl) {
        if(ctrl && !ctrl.isSelected()) {
          ctrl.select();
        }
      });
    };
    
    scope.toggleAll = function () {
      return scope.allSelected() ? scope.unSelectAll() : scope.selectAll();
    };
    
    scope.unSelectAll = function () {
      tableCtrl.getBodyRows().map(mdSelectCtrl).forEach(function (ctrl) {
        if(ctrl && ctrl.isSelected()) {
          ctrl.deselect();
        }
      });
    };
    
    scope.$watchGroup([enableRowSelection, tableCtrl.enableMultiSelect], function (newValue) {
      if(newValue[0] !== oldValue[0]) {
        if(newValue[0]) {
          addCheckboxColumn();
          
          if(newValue[1]) {
            attatchCheckbox();
          }
        } else {
          removeCheckboxColumn();
        }
      } else if(newValue[0] && newValue[1] !== oldValue[1]) {
        if(newValue[1]) {
          attatchCheckbox();
        } else {
          detachCheckbox();
        }
      }
      
      angular.copy(newValue, oldValue);
    });
  }
  
  return {
    bindToController: true,
    compile: compile,
    controller: Controller,
    controllerAs: '$mdHead',
    require: '^^mdTable',
    restrict: 'A',
    scope: {
      order: '=?mdOrder',
      onReorder: '=?mdOnReorder'
    }
  };
}

mdHead.$inject = ['$compile'];

angular.module('md.data.table').directive('mdRow', mdRow);

function mdRow() {

  function compile(tElement) {
    tElement.addClass('md-row');
    return postLink;
  }
  
  function postLink(scope, element, attrs, tableCtrl) {
    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }
    
    function isBodyRow() {
      return tableCtrl.getBodyRows().indexOf(element[0]) !== -1;
    }
    
    function isChild(node) {
      return element[0].contains(node[0]);
    }
    
    if(isBodyRow()) {
      var cell = angular.element('<td class="md-cell">');
      
      scope.$watch(enableRowSelection, function (enable) {
        // if a row is not selectable, prepend an empty cell to it
        if(enable && !attrs.mdSelect) {
          if(!isChild(cell)) {
            element.prepend(cell);
          }
          return;
        }
        
        if(isChild(cell)) {
          cell.remove();
        }
      });
    }
  }

  return {
    compile: compile,
    require: '^^mdTable',
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdSelect', mdSelect);

function mdSelect($compile, $parse) {

  // empty controller to bind scope properties to
  function Controller() {

  }

  function postLink(scope, element, attrs, ctrls) {
    var self = ctrls.shift();
    var tableCtrl = ctrls.shift();
    var getId = $parse(attrs.mdSelectId);

    self.id = getId(self.model);

    if(tableCtrl.$$rowSelect && self.id) {
      if(tableCtrl.$$hash.has(self.id)) {
        var index = tableCtrl.selected.indexOf(tableCtrl.$$hash.get(self.id));

        // if the item is no longer selected remove it
        if(index === -1) {
          tableCtrl.$$hash.purge(self.id);
        }

        // if the item is not a reference to the current model update the reference
        else if(!tableCtrl.$$hash.equals(self.id, self.model)) {
          tableCtrl.$$hash.update(self.id, self.model);
          tableCtrl.selected.splice(index, 1, self.model);
        }

      } else {

        // check if the item has been selected
        tableCtrl.selected.some(function (item, index) {
          if(getId(item) === self.id) {
            tableCtrl.$$hash.update(self.id, self.model);
            tableCtrl.selected.splice(index, 1, self.model);

            return true;
          }
        });
      }
    }

    self.isSelected = function () {
      if(!tableCtrl.$$rowSelect) {
        return false;
      }

      if(self.id) {
        return tableCtrl.$$hash.has(self.id);
      }

      return tableCtrl.selected.indexOf(self.model) !== -1;
    };

    self.select = function () {
      if(self.disabled) {
        return;
      }

      if(tableCtrl.enableMultiSelect()) {
        tableCtrl.selected.push(self.model);
      } else {
        tableCtrl.selected.splice(0, tableCtrl.selected.length, self.model);
      }

      if(angular.isFunction(self.onSelect)) {
        self.onSelect(self.model);
      }
    };

    self.deselect = function () {
      if(self.disabled) {
        return;
      }

      tableCtrl.selected.splice(tableCtrl.selected.indexOf(self.model), 1);

      if(angular.isFunction(self.onDeselect)) {
        self.onDeselect(self.model);
      }
    };

    self.toggle = function (event) {
      if(event && event.stopPropagation) {
        event.stopPropagation();
      }

      return self.isSelected() ? self.deselect() : self.select();
    };

    function autoSelect() {
      return attrs.mdAutoSelect === '' || self.autoSelect;
    }

    function createCheckbox() {
      var checkbox = angular.element('<md-checkbox>').attr({
        'aria-label': 'Select Row',
        'ng-click': '$mdSelect.toggle($event)',
        'ng-checked': '$mdSelect.isSelected()',
        'ng-disabled': '$mdSelect.disabled'
      });

      return angular.element('<td class="md-cell md-checkbox-cell">').append($compile(checkbox)(scope));
    }

    function disableSelection() {
      Array.prototype.some.call(element.children(), function (child) {
        return child.classList.contains('md-checkbox-cell') && element[0].removeChild(child);
      });

      if(autoSelect()) {
        element.off('click', toggle);
      }
    }

    function enableSelection() {
      element.prepend(createCheckbox());

      if(autoSelect()) {
        element.on('click', toggle);
      }
    }

    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }

    function onSelectChange(selected) {
      if(!self.id) {
        return;
      }

      if(tableCtrl.$$hash.has(self.id)) {
        // check if the item has been deselected
        if(selected.indexOf(tableCtrl.$$hash.get(self.id)) === -1) {
          tableCtrl.$$hash.purge(self.id);
        }

        return;
      }

      // check if the item has been selected
      if(selected.indexOf(self.model) !== -1) {
        tableCtrl.$$hash.update(self.id, self.model);
      }
    }

    function toggle(event) {
      scope.$applyAsync(function () {
        self.toggle(event);
      });
    }

    scope.$watch(enableRowSelection, function (enable) {
      if(enable) {
        enableSelection();
      } else {
        disableSelection();
      }
    });

    scope.$watch(autoSelect, function (newValue, oldValue) {
      if(newValue === oldValue) {
        return;
      }

      if(tableCtrl.$$rowSelect && newValue) {
        element.on('click', toggle);
      } else {
        element.off('click', toggle);
      }
    });

    scope.$watch(self.isSelected, function (isSelected) {
      return isSelected ? element.addClass('md-selected') : element.removeClass('md-selected');
    });

    scope.$watch(tableCtrl.enableMultiSelect, function (multiple) {
      if(tableCtrl.$$rowSelect && !multiple) {
        // remove all but the first selected item
        tableCtrl.selected.splice(1);
      }
    });

    tableCtrl.registerModelChangeListener(onSelectChange);

    element.on('$destroy', function () {
      tableCtrl.removeModelChangeListener(onSelectChange);
    });
  }

  return {
    bindToController: true,
    controller: Controller,
    controllerAs: '$mdSelect',
    link: postLink,
    require: ['mdSelect', '^^mdTable'],
    restrict: 'A',
    scope: {
      model: '=mdSelect',
      disabled: '=ngDisabled',
      onSelect: '=?mdOnSelect',
      onDeselect: '=?mdOnDeselect',
      autoSelect: '=mdAutoSelect'
    }
  };
}

mdSelect.$inject = ['$compile', '$parse'];

angular.module('md.data.table').directive('mdTable', mdTable);

function Hash() {
  var keys = {};
  
  this.equals = function (key, item) {
    return keys[key] === item;
  };

  this.get = function (key) {
    return keys[key];
  };
  
  this.has = function (key) {
    return keys.hasOwnProperty(key);
  };

  this.purge = function (key) {
    delete keys[key];
  };
  
  this.update = function (key, item) {
    keys[key] = item;
  };
}

function mdTable() {
  
  function compile(tElement, tAttrs) {
    tElement.addClass('md-table');
    
    if(tAttrs.hasOwnProperty('mdProgress')) {
      var body = tElement.find('tbody')[0];
      var progress = angular.element('<thead class="md-table-progress">');
      
      if(body) {
        tElement[0].insertBefore(progress[0], body);
      }
    }
  }
  
  function Controller($attrs, $element, $q, $scope) {
    var self = this;
    var queue = [];
    var watchListener;
    var modelChangeListeners = [];
    
    self.$$hash = new Hash();
    self.$$columns = {};
    
    function enableRowSelection() {
      self.$$rowSelect = true;
      
      watchListener = $scope.$watchCollection('$mdTable.selected', function (selected) {
        modelChangeListeners.forEach(function (listener) {
          listener(selected);
        });
      });
      
      $element.addClass('md-row-select');
    }
    
    function disableRowSelection() {
      self.$$rowSelect = false;
      
      if(angular.isFunction(watchListener)) {
        watchListener();
      }
      
      $element.removeClass('md-row-select');
    }
    
    function resolvePromises() {
      if(!queue.length) {
        return $scope.$applyAsync();
      }
      
      queue[0]['finally'](function () {
        queue.shift();
        resolvePromises();
      });
    }
    
    function rowSelect() {
      return $attrs.mdRowSelect === '' || self.rowSelect;
    }
    
    function validateModel() {
      if(!self.selected) {
        return console.error('Row selection: ngModel is not defined.');
      }
      
      if(!angular.isArray(self.selected)) {
        return console.error('Row selection: Expected an array. Recived ' + typeof self.selected + '.');
      }
      
      return true;
    }
    
    self.columnCount = function () {
      return self.getRows($element[0]).reduce(function (count, row) {
        return row.cells.length > count ? row.cells.length : count;
      }, 0);
    };
    
    self.getRows = function (element) {
      return Array.prototype.filter.call(element.rows, function (row) {
        return !row.classList.contains('ng-leave');
      });
    };
    
    self.getBodyRows = function () {
      return Array.prototype.reduce.call($element.prop('tBodies'), function (result, tbody) {
        return result.concat(self.getRows(tbody));
      }, []);
    };
    
    self.getElement = function () {
      return $element;
    };
    
    self.getHeaderRows = function () {
      return self.getRows($element.prop('tHead'));
    };
    
    self.enableMultiSelect = function () {
      return $attrs.multiple === '' || $scope.$eval($attrs.multiple);
    };
    
    self.waitingOnPromise = function () {
      return !!queue.length;
    };
    
    self.queuePromise = function (promise) {
      if(!promise) {
        return;
      }
      
      if(queue.push(angular.isArray(promise) ? $q.all(promise) : $q.when(promise)) === 1) {
        resolvePromises();
      }
    };
    
    self.registerModelChangeListener = function (listener) {
      modelChangeListeners.push(listener);
    };
    
    self.removeModelChangeListener = function (listener) {
      var index = modelChangeListeners.indexOf(listener);
      
      if(index !== -1) {
        modelChangeListeners.splice(index, 1);
      }
    };
    
    if($attrs.hasOwnProperty('mdProgress')) {
      $scope.$watch('$mdTable.progress', self.queuePromise);
    }
    
    $scope.$watch(rowSelect, function (enable) {
      if(enable && !!validateModel()) {
        enableRowSelection();
      } else {
        disableRowSelection();
      }
    });
  }
  
  Controller.$inject = ['$attrs', '$element', '$q', '$scope'];
  
  return {
    bindToController: true,
    compile: compile,
    controller: Controller,
    controllerAs: '$mdTable',
    restrict: 'A',
    scope: {
      progress: '=?mdProgress',
      selected: '=ngModel',
      rowSelect: '=mdRowSelect'
    }
  };
}

angular.module('md.data.table').directive('mdTablePagination', mdTablePagination);

function mdTablePagination() {

  function compile(tElement) {
    tElement.addClass('md-table-pagination');
  }

  function Controller($attrs, $mdUtil, $scope) {
    var self = this;
    var defaultLabel = {
      page: 'Page:',
      rowsPerPage: 'Rows per page:',
      of: 'of'
    };

    self.label = angular.copy(defaultLabel);

    function isPositive(number) {
      return parseInt(number, 10) > 0;
    }

    self.eval = function (expression) {
      return $scope.$eval(expression);
    };

    self.first = function () {
      self.page = 1;
      self.onPaginationChange();
    };

    self.hasNext = function () {
      return self.page * self.limit < self.total;
    };

    self.hasPrevious = function () {
      return self.page > 1;
    };

    self.last = function () {
      self.page = self.pages();
      self.onPaginationChange();
    };

    self.max = function () {
      return self.hasNext() ? self.page * self.limit : self.total;
    };

    self.min = function () {
      return isPositive(self.total) ? self.page * self.limit - self.limit + 1 : 0;
    };

    self.next = function () {
      self.page++;
      self.onPaginationChange();
    };

    self.onPaginationChange = function () {
      if(angular.isFunction(self.onPaginate)) {
        $mdUtil.nextTick(function () {
          self.onPaginate(self.page, self.limit);
        });
      }
    };

    self.pages = function () {
      return isPositive(self.total) ? Math.ceil(self.total / (isPositive(self.limit) ? self.limit : 1)) : 1;
    };

    self.previous = function () {
      self.page--;
      self.onPaginationChange();
    };

    self.showBoundaryLinks = function () {
      return $attrs.mdBoundaryLinks === '' || self.boundaryLinks;
    };

    self.showPageSelect = function () {
      return $attrs.mdPageSelect === '' || self.pageSelect;
    };

    $scope.$watch('$pagination.limit', function (newValue, oldValue) {
      if(isNaN(newValue) || isNaN(oldValue) || newValue === oldValue) {
        return;
      }

      // find closest page from previous min
      self.page = Math.floor(((self.page * oldValue - oldValue) + newValue) / (isPositive(newValue) ? newValue : 1));
      self.onPaginationChange();
    });

    $attrs.$observe('mdLabel', function (label) {
      angular.extend(self.label, defaultLabel, $scope.$eval(label));
    });

    $scope.$watch('$pagination.total', function (newValue, oldValue) {
      if(isNaN(newValue) || newValue === oldValue) {
        return;
      }

      if(self.page > self.pages()) {
        self.last();
      }
    });
  }

  Controller.$inject = ['$attrs', '$mdUtil', '$scope'];

  return {
    bindToController: {
      boundaryLinks: '=?mdBoundaryLinks',
      disabled: '=ngDisabled',
      limit: '=mdLimit',
      page: '=mdPage',
      pageSelect: '=?mdPageSelect',
      onPaginate: '=?mdOnPaginate',
      limitOptions: '=?mdLimitOptions',
      total: '@mdTotal'
    },
    compile: compile,
    controller: Controller,
    controllerAs: '$pagination',
    restrict: 'E',
    scope: {},
    templateUrl: 'md-table-pagination.html'
  };
}

angular.module('md.data.table').directive('mdTableProgress', mdTableProgress);

function mdTableProgress() {

  function postLink(scope, element, attrs, tableCtrl) {
    scope.columnCount = tableCtrl.columnCount;
    scope.deferred = tableCtrl.waitingOnPromise;
  }

  return {
    link: postLink,
    require: '^^mdTable',
    restrict: 'C',
    scope: {},
    templateUrl: 'md-table-progress.html'
  };
}

angular.module('md.data.table').directive('virtualPageSelect', virtualPageSelect);

function virtualPageSelect() {

  function Controller($element, $scope) {
    var self = this;
    var content = $element.find('md-content');

    self.pages = [];

    function getMin(pages, total) {
      return Math.min(pages, isFinite(total) && isPositive(total) ? total : 1);
    }

    function isPositive(number) {
      return number > 0;
    }

    function setPages(max) {
      if(self.pages.length > max) {
        return self.pages.splice(max);
      }

      for(var i = self.pages.length; i < max; i++) {
        self.pages.push(i + 1);
      }
    }

    content.on('scroll', function () {
      if((content.prop('clientHeight') + content.prop('scrollTop')) >= content.prop('scrollHeight')) {
        $scope.$applyAsync(function () {
          setPages(getMin(self.pages.length + 10, self.total));
        });
      }
    });

    $scope.$watch('$pageSelect.total', function (total) {
      setPages(getMin(Math.max(self.pages.length, 10), total));
    });

    $scope.$watch('$pagination.page', function (page) {
      for(var i = self.pages.length; i < page; i++) {
        self.pages.push(i + 1);
      }
    });
  }

  Controller.$inject = ['$element', '$scope'];

  return {
    bindToController: {
      total: '@'
    },
    controller: Controller,
    controllerAs: '$pageSelect'
  };
}

})(window, angular);
"use strict";angular.module("ngMdIcons",[]).directive("ngMdIcon",["ngMdIconService",function(h){var c=h.getShapes();return{restrict:"AE",link:function(v,a,z){var t,d,p,M=function(){if(void 0!==z.icon){t=z.icon;var v=t.match(/ic_(.*)_([0-9]+)px.svg/m);null!==v&&(t=v[1],d=v[2])}else t="help";void 0===c[t]&&(t="help"),void 0!==z.size?d=z.size:null!==d&&(d=24),p=void 0!==z.viewBox?z.viewBox:h.getViewBox(t)?h.getViewBox(t):"0 0 24 24",a.html('<svg xmlns="http://www.w3.org/2000/svg" viewBox="'+p+'" width="'+d+'" height="'+d+'">'+c[t]+"</svg>")},l=function(h){if(void 0===c[h]&&(h="help"),h!==t){a.html('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="'+d+'" height="'+d+'"><g id="'+h+'" style="display:none">'+c[h]+'</g><g id="'+t+'" style="display:none">'+c[t]+"</g></svg>");var v=JSON.parse(z.options||null);try{new SVGMorpheus(a.children()[0]).to(h,v)}catch(p){a.html('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="'+d+'" height="'+d+'">'+c[h]+"</svg>")}t=h}},H=function(h){h!==d&&(a.children()[0].setAttribute("width",h),a.children()[0].setAttribute("height",h),d=h)};M(),void 0!==z.icon&&z.$observe("icon",l),void 0!==z.size&&z.$observe("size",H)}}}]).provider("ngMdIconService",function(){function h(h,c){return s[h]=c,H}function c(h){return s=angular.extend(s,h),H}function v(h,c){return e[h]=c,H}function a(h){return e=angular.extend(e,h),H}function z(h){return s[h]}function t(){return s}function d(h){return e[h]}function p(){return e}function M(){return{amazon:'<path d="M13.23 10.56V10c-1.94 0-3.99.39-3.99 2.67 0 1.16.61 1.95 1.63 1.95.76 0 1.43-.47 1.86-1.22.52-.93.5-1.8.5-2.84m2.7 6.53c-.18.16-.43.17-.63.06-.89-.74-1.05-1.08-1.54-1.79-1.47 1.5-2.51 1.95-4.42 1.95-2.25 0-4.01-1.39-4.01-4.17 0-2.18 1.17-3.64 2.86-4.38 1.46-.64 3.49-.76 5.04-.93V7.5c0-.66.05-1.41-.33-1.96-.32-.49-.95-.7-1.5-.7-1.02 0-1.93.53-2.15 1.61-.05.24-.25.48-.47.49l-2.6-.28c-.22-.05-.46-.22-.4-.56.6-3.15 3.45-4.1 6-4.1 1.3 0 3 .35 4.03 1.33C17.11 4.55 17 6.18 17 7.95v4.17c0 1.25.5 1.81 1 2.48.17.25.21.54 0 .71l-2.06 1.78h-.01"/><path d="M20.16 19.54C18 21.14 14.82 22 12.1 22c-3.81 0-7.25-1.41-9.85-3.76-.2-.18-.02-.43.25-.29 2.78 1.63 6.25 2.61 9.83 2.61 2.41 0 5.07-.5 7.51-1.53.37-.16.66.24.32.51"/><path d="M21.07 18.5c-.28-.36-1.85-.17-2.57-.08-.19.02-.22-.16-.03-.3 1.24-.88 3.29-.62 3.53-.33.24.3-.07 2.35-1.24 3.32-.18.16-.35.07-.26-.11.26-.67.85-2.14.57-2.5z"/>',apple:'<path d="M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83"/>',"facebook-box":'<path d="M19 4v3h-2a1 1 0 0 0-1 1v2h3v3h-3v7h-3v-7h-2v-3h2V7.5C13 5.56 14.57 4 16.5 4M20 2H4a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V4c0-1.11-.9-2-2-2z"/>',"facebook-messenger":'<path d="M12 2C6.5 2 2 6.14 2 11.25c0 2.88 1.42 5.45 3.65 7.15l.06 3.6 3.45-1.88-.03-.01c.91.25 1.87.39 2.87.39 5.5 0 10-4.14 10-9.25S17.5 2 12 2m1.03 12.41l-2.49-2.63-5.04 2.63 5.38-5.63 2.58 2.47 4.85-2.47-5.28 5.63z"/>',facebook:'<path d="M17 2v4h-2c-.69 0-1 .81-1 1.5V10h3v4h-3v8h-4v-8H7v-4h3V6a4 4 0 0 1 4-4h3z"/>',"github-box":'<path d="M4 2h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2h-5.15c-.35-.08-.35-.76-.35-1v-2.74c0-.93-.33-1.54-.69-1.85 2.23-.25 4.57-1.09 4.57-4.91 0-1.11-.38-2-1.03-2.71.1-.25.45-1.29-.1-2.64 0 0-.84-.27-2.75 1.02-.79-.22-1.65-.33-2.5-.33-.85 0-1.71.11-2.5.33-1.91-1.29-2.75-1.02-2.75-1.02-.55 1.35-.2 2.39-.1 2.64-.65.71-1.03 1.6-1.03 2.71 0 3.81 2.33 4.67 4.55 4.92-.28.25-.54.69-.63 1.34-.57.24-2.04.69-2.91-.83 0 0-.53-.96-1.53-1.03 0 0-.98-.02-.1.6 0 0 .68.31 1.14 1.47 0 0 .59 1.94 3.36 1.34V21c0 .24 0 .92-.36 1H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/>',"github-circle":'<path d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2z"/>',"google-plus-box":'<path d="M20 2H4a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V4c0-1.11-.9-2-2-2M9.07 19.2C6.27 19.2 5 17.64 5 16.18c0-.45.14-1.59 1.5-2.38.75-.47 1.83-.8 3.12-.91-.19-.25-.34-.55-.34-.99 0-.15.02-.31.06-.46h-.39C7 11.44 5.8 9.89 5.8 8.39c0-1.73 1.29-3.59 4.11-3.59h4.22l-.34.34-.71.71-.08.06h-.7c.41.42.9 1.09.9 2.16 0 1.4-.74 2.09-1.56 2.73-.14.12-.42.38-.42.7 0 .32.24.5.39.64.13.11.29.22.47.36.81.55 1.92 1.33 1.92 2.86 0 1.77-1.29 3.84-4.93 3.84M19 12h-2v2h-1v-2h-2v-1h2V9h1v2h2"/><path d="M10.57 13.81c-.11-.01-.19-.01-.32-.01h-.02c-.26 0-1.15.05-1.82.27-.64.24-1.41.72-1.41 1.7C7 16.85 8.04 18 9.96 18c1.54 0 2.44-1 2.44-2 0-.75-.46-1.21-1.83-2.19"/><path d="M11.2 8.87c0-1.02-.63-3.02-2.08-3.02-.62 0-1.32.44-1.32 1.65 0 1.2.62 2.95 1.97 2.95.06 0 1.43-.01 1.43-1.58z"/>',"google-plus":'<path d="M13.3 13.45l-1.08-.85c-.36-.3-.82-.69-.82-1.42s.55-1.29.97-1.62c1.31-1.02 2.57-2.1 2.57-4.34 0-2.07-1.27-3.26-2.04-3.92h1.75L15.9.05H9.67c-4.36 0-6.6 2.71-6.6 5.72 0 2.33 1.79 4.83 4.98 4.83h.8c-.13.35-.35.84-.35 1.3 0 1.01.42 1.43.92 2-1.42.1-4.01.43-5.92 1.6-1.86 1.1-2.3 2.63-2.3 3.75 0 2.3 2.06 4.5 6.57 4.5 5.35 0 8.03-2.96 8.03-5.88 0-2.16-1.13-3.27-2.5-4.42M5.65 4.31c0-2.21 1.31-3.21 2.69-3.21 2.66 0 4.01 3.45 4.01 5.53 0 2.57-2.07 3.07-2.89 3.07C7 9.7 5.65 6.64 5.65 4.31M9.3 22.3c-3.33 0-5.45-1.49-5.45-3.7 0-2.2 1.96-2.91 2.65-3.16 1.3-.44 3-.49 3.27-.49.3 0 .46 0 .73.02 2.34 1.69 3.35 2.44 3.35 4.03 0 1.77-1.82 3.3-4.55 3.3"/><path d="M21 10V7h-2v3h-3v2h3v3h2v-3h3v-2h-3z"/>',hangouts:'<path d="M15 11l-1 2h-1.5l1-2H12V8h3m-4 3l-1 2H8.5l1-2H8V8h3m.5-6A8.5 8.5 0 0 0 3 10.5a8.5 8.5 0 0 0 8.5 8.5h.5v3.5c4.86-2.35 8-7.5 8-12C20 5.8 16.19 2 11.5 2z"/>',"linkedin-box":'<path d="M19 19h-3v-5.3a1.5 1.5 0 0 0-1.5-1.5 1.5 1.5 0 0 0-1.5 1.5V19h-3v-9h3v1.2c.5-.84 1.59-1.4 2.5-1.4a3.5 3.5 0 0 1 3.5 3.5M6.5 8.31c-1 0-1.81-.81-1.81-1.81A1.81 1.81 0 0 1 6.5 4.69c1 0 1.81.81 1.81 1.81A1.81 1.81 0 0 1 6.5 8.31M8 19H5v-9h3m12-8H4c-1.11 0-2 .89-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V4c0-1.11-.9-2-2-2z"/>',linkedin:'<path d="M21 21h-4v-6.75c0-1.06-1.19-1.94-2.25-1.94S13 13.19 13 14.25V21H9V9h4v2c.66-1.07 2.36-1.76 3.5-1.76 2.5 0 4.5 2.04 4.5 4.51V21"/><path d="M7 21H3V9h4v12"/><path d="M5 3a2 2 0 0 1 2 2 2 2 0 0 1-2 2 2 2 0 0 1-2-2 2 2 0 0 1 2-2z"/>',login:'<path d="M10 17.25V14H3v-4h7V6.75L15.25 12 10 17.25"/><path d="M8 2h9a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-4h2v4h9V4H8v4H6V4a2 2 0 0 1 2-2z"/>',logout:'<path d="M17 17.25V14h-7v-4h7V6.75L22.25 12 17 17.25"/><path d="M13 2a2 2 0 0 1 2 2v4h-2V4H4v16h9v-4h2v4a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9z"/>',office:'<path d="M3 18l4-1.25V7l7-2v14.5L3.5 18.25 14 22l6-1.25V3.5L13.95 2 3 5.75V18z"/>',twitter:'<path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21-.36.1-.74.15-1.13.15-.27 0-.54-.03-.8-.08.54 1.69 2.11 2.95 4 2.98-1.46 1.16-3.31 1.84-5.33 1.84-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>',whatsapp:'<path d="M16.75 13.96c.25.13.41.2.46.3.06.11.04.61-.21 1.18-.2.56-1.24 1.1-1.7 1.12-.46.02-.47.36-2.96-.73-2.49-1.09-3.99-3.75-4.11-3.92-.12-.17-.96-1.38-.92-2.61.05-1.22.69-1.8.95-2.04.24-.26.51-.29.68-.26h.47c.15 0 .36-.06.55.45l.69 1.87c.06.13.1.28.01.44l-.27.41-.39.42c-.12.12-.26.25-.12.5.12.26.62 1.09 1.32 1.78.91.88 1.71 1.17 1.95 1.3.24.14.39.12.54-.04l.81-.94c.19-.25.35-.19.58-.11l1.67.88"/><path d="M12 4a8 8 0 0 0-8 8c0 1.72.54 3.31 1.46 4.61L4.5 19.5l2.89-.96C8.69 19.46 10.28 20 12 20a8 8 0 0 0 8-8 8 8 0 0 0-8-8zm0-2a10 10 0 0 1 10 10 10 10 0 0 1-10 10c-1.97 0-3.8-.57-5.35-1.55L2 22l1.55-4.65C2.57 15.8 2 13.97 2 12A10 10 0 0 1 12 2"/>',windows:'<path d="M3 12V6.75l6-1.32v6.48L3 12"/><path d="M20 3v8.75l-10 .15V5.21L20 3"/><path d="M3 13l6 .09v6.81l-6-1.15V13"/><path d="M20 13.25V22l-10-1.91V13.1l10 .15z"/>',signal_wifi_0_bar:'<path fill-opacity=".3" d="M12.01 21.49L23.64 7c-.45-.34-4.93-4-11.64-4C5.28 3 .81 6.66.36 7l11.63 14.49.01.01.01-.01z"/>',signal_wifi_1_bar:'<path fill-opacity=".3" d="M12.01 21.49L23.64 7c-.45-.34-4.93-4-11.64-4C5.28 3 .81 6.66.36 7l11.63 14.49.01.01.01-.01z"/><path d="M6.67 14.86L12 21.49v.01l.01-.01 5.33-6.63C17.06 14.65 15.03 13 12 13s-5.06 1.65-5.33 1.86z"/>',signal_wifi_2_bar:'<path fill-opacity=".3" d="M12.01 21.49L23.64 7c-.45-.34-4.93-4-11.64-4C5.28 3 .81 6.66.36 7l11.63 14.49.01.01.01-.01z"/><path d="M4.79 12.52l7.2 8.98H12l.01-.01 7.2-8.98C18.85 12.24 16.1 10 12 10s-6.85 2.24-7.21 2.52z"/>',signal_wifi_3_bar:'<path fill-opacity=".3" d="M12.01 21.49L23.64 7c-.45-.34-4.93-4-11.64-4C5.28 3 .81 6.66.36 7l11.63 14.49.01.01.01-.01z"/><path d="M3.53 10.95l8.46 10.54.01.01.01-.01 8.46-10.54C20.04 10.62 16.81 8 12 8c-4.81 0-8.04 2.62-8.47 2.95z"/>',signal_cellular_connected_no_internet_0_bar:'<path fill-opacity=".3" d="M22 8V2L2 22h16V8z"/><path d="M20 22h2v-2h-2v2zm0-12v8h2v-8h-2z"/>',signal_cellular_connected_no_internet_1_bar:'<path fill-opacity=".3" d="M22 8V2L2 22h16V8z"/><path d="M20 10v8h2v-8h-2zm-8 12V12L2 22h10zm8 0h2v-2h-2v2z"/>',signal_cellular_connected_no_internet_2_bar:'<path fill-opacity=".3" d="M22 8V2L2 22h16V8z"/><path d="M14 22V10L2 22h12zm6-12v8h2v-8h-2zm0 12h2v-2h-2v2z"/>',signal_cellular_connected_no_internet_3_bar:'<path fill-opacity=".3" d="M22 8V2L2 22h16V8z"/><path d="M17 22V7L2 22h15zm3-12v8h2v-8h-2zm0 12h2v-2h-2v2z"/>',signal_cellular_0_bar:'<path fill-opacity=".3" d="M2 22h20V2z"/>',signal_cellular_1_bar:'<path fill-opacity=".3" d="M2 22h20V2z"/><path d="M12 12L2 22h10z"/>',signal_cellular_2_bar:'<path fill-opacity=".3" d="M2 22h20V2z"/><path d="M14 10L2 22h12z"/>',signal_cellular_3_bar:'<path fill-opacity=".3" d="M2 22h20V2z"/><path d="M17 7L2 22h15z"/>',now_wallpaper:'<path d="M4 4h7V2H4c-1.1 0-2 .9-2 2v7h2V4z"/><path d="M10 13l-4 5h12l-3-4-2.03 2.71L10 13z"/><path d="M17 8.5c0-.83-.67-1.5-1.5-1.5S14 7.67 14 8.5s.67 1.5 1.5 1.5S17 9.33 17 8.5z"/><path d="M20 2h-7v2h7v7h2V4c0-1.1-.9-2-2-2z"/><path d="M20 20h-7v2h7c1.1 0 2-.9 2-2v-7h-2v7z"/><path d="M4 13H2v7c0 1.1.9 2 2 2h7v-2H4v-7z"/>',now_widgets:'<path d="M13 13v8h8v-8h-8z"/><path d="M3 21h8v-8H3v8z"/><path d="M3 3v8h8V3H3z"/><path d="M16.66 1.69L11 7.34 16.66 13l5.66-5.66-5.66-5.65z"/>',battery_20:'<path d="M7 17v3.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V17H7z"/><path fill-opacity=".3" d="M17 5.33C17 4.6 16.4 4 15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V17h10V5.33z"/>',battery_30:'<path fill-opacity=".3" d="M17 5.33C17 4.6 16.4 4 15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V15h10V5.33z"/><path d="M7 15v5.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V15H7z"/>',battery_50:'<path fill-opacity=".3" d="M17 5.33C17 4.6 16.4 4 15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V13h10V5.33z"/><path d="M7 13v7.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V13H7z"/>',battery_60:'<path fill-opacity=".3" d="M17 5.33C17 4.6 16.4 4 15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V11h10V5.33z"/><path d="M7 11v9.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V11H7z"/>',battery_80:'<path fill-opacity=".3" d="M17 5.33C17 4.6 16.4 4 15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V9h10V5.33z"/><path d="M7 9v11.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V9H7z"/>',battery_90:'<path fill-opacity=".3" d="M17 5.33C17 4.6 16.4 4 15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V8h10V5.33z"/><path d="M7 8v12.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V8H7z"/>',battery_alert:'<path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4zM13 18h-2v-2h2v2zm0-4h-2V9h2v5z"/>',battery_charging_20:'<path d="M11 20v-3H7v3.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V17h-4.4L11 20z"/><path fill-opacity=".3" d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V17h4v-2.5H9L13 7v5.5h2L12.6 17H17V5.33C17 4.6 16.4 4 15.67 4z"/>',battery_charging_30:'<path fill-opacity=".3" d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v9.17h2L13 7v5.5h2l-1.07 2H17V5.33C17 4.6 16.4 4 15.67 4z"/><path d="M11 20v-5.5H7v6.17C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V14.5h-3.07L11 20z"/>',battery_charging_50:'<path d="M14.47 13.5L11 20v-5.5H9l.53-1H7v7.17C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V13.5h-2.53z"/><path fill-opacity=".3" d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v8.17h2.53L13 7v5.5h2l-.53 1H17V5.33C17 4.6 16.4 4 15.67 4z"/>',battery_charging_60:'<path fill-opacity=".3" d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V11h3.87L13 7v4h4V5.33C17 4.6 16.4 4 15.67 4z"/><path d="M13 12.5h2L11 20v-5.5H9l1.87-3.5H7v9.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V11h-4v1.5z"/>',battery_charging_80:'<path fill-opacity=".3" d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V9h4.93L13 7v2h4V5.33C17 4.6 16.4 4 15.67 4z"/><path d="M13 12.5h2L11 20v-5.5H9L11.93 9H7v11.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V9h-4v3.5z"/>',battery_charging_90:'<path fill-opacity=".3" d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V8h5.47L13 7v1h4V5.33C17 4.6 16.4 4 15.67 4z"/><path d="M13 12.5h2L11 20v-5.5H9L12.47 8H7v12.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V8h-4v4.5z"/>',account_child:'<circle cx="12" cy="13.49" r="1.5"/><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 2.5c1.24 0 2.25 1.01 2.25 2.25S13.24 9 12 9 9.75 7.99 9.75 6.75 10.76 4.5 12 4.5zm5 10.56v2.5c-.45.41-.96.77-1.5 1.05v-.68c0-.34-.17-.65-.46-.92-.65-.62-1.89-1.02-3.04-1.02-.96 0-1.96.28-2.65.73l-.17.12-.21.17c.78.47 1.63.72 2.54.82l1.33.15c.37.04.66.36.66.75 0 .29-.16.53-.4.66-.28.15-.64.09-.95.09-.35 0-.69-.01-1.03-.05-.5-.06-.99-.17-1.46-.33-.49-.16-.97-.38-1.42-.64-.22-.13-.44-.27-.65-.43l-.31-.24c-.04-.02-.28-.18-.28-.23v-4.28c0-1.58 2.63-2.78 5-2.78s5 1.2 5 2.78v1.78z"/>',"3d_rotation":'<path d="M7.52 21.48C4.25 19.94 1.91 16.76 1.55 13H.05C.56 19.16 5.71 24 12 24l.66-.03-3.81-3.81-1.33 1.32z"/><path d="M16.57 12.2c0 .42-.05.79-.14 1.13-.1.33-.24.62-.43.85-.19.23-.43.41-.71.53-.29.12-.62.18-.99.18h-.91V9.12h.97c.72 0 1.27.23 1.64.69.38.46.57 1.12.57 1.99v.4zm.39-3.16c-.32-.33-.7-.59-1.14-.77-.43-.18-.92-.27-1.46-.27H12v8h2.3c.55 0 1.06-.09 1.51-.27.45-.18.84-.43 1.16-.76.32-.33.57-.73.74-1.19.17-.47.26-.99.26-1.57v-.4c0-.58-.09-1.1-.26-1.57-.18-.47-.43-.87-.75-1.2zm-8.55 5.92c-.19 0-.37-.03-.52-.08-.16-.06-.29-.13-.4-.24-.11-.1-.2-.22-.26-.37-.06-.14-.09-.3-.09-.47h-1.3c0 .36.07.68.21.95.14.27.33.5.56.69.24.18.51.32.82.41.3.1.62.15.96.15.37 0 .72-.05 1.03-.15.32-.1.6-.25.83-.44.23-.19.42-.43.55-.72.13-.29.2-.61.2-.97 0-.19-.02-.38-.07-.56-.05-.18-.12-.35-.23-.51-.1-.16-.24-.3-.4-.43-.17-.13-.37-.23-.61-.31.2-.09.37-.2.52-.33.15-.13.27-.27.37-.42.1-.15.17-.3.22-.46.05-.16.07-.32.07-.48 0-.36-.06-.68-.18-.96-.12-.28-.29-.51-.51-.69-.2-.19-.47-.33-.77-.43C9.1 8.05 8.76 8 8.39 8c-.36 0-.69.05-1 .16-.3.11-.57.26-.79.45-.21.19-.38.41-.51.67-.12.26-.18.54-.18.85h1.3c0-.17.03-.32.09-.45s.14-.25.25-.34c.11-.09.23-.17.38-.22.15-.05.3-.08.48-.08.4 0 .7.1.89.31.19.2.29.49.29.86 0 .18-.03.34-.08.49-.05.15-.14.27-.25.37-.11.1-.25.18-.41.24-.16.06-.36.09-.58.09H7.5v1.03h.77c.22 0 .42.02.6.07s.33.13.45.23c.12.11.22.24.29.4.07.16.1.35.1.57 0 .41-.12.72-.35.93-.23.23-.55.33-.95.33z"/><path d="M12 0l-.66.03 3.81 3.81 1.33-1.33c3.27 1.55 5.61 4.72 5.96 8.48h1.5C23.44 4.84 18.29 0 12 0z"/>',accessibility:'<path d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z"/><path d="M21 9h-6v13h-2v-6h-2v6H9V9H3V7h18v2z"/>',accessible:'<circle cx="12" cy="4" r="2"/><path d="M19 13v-2c-1.54.02-3.09-.75-4.07-1.83l-1.29-1.43c-.17-.19-.38-.34-.61-.45-.01 0-.01-.01-.02-.01H13c-.35-.2-.75-.3-1.19-.26C10.76 7.11 10 8.04 10 9.09V15c0 1.1.9 2 2 2h5v5h2v-5.5c0-1.1-.9-2-2-2h-3v-3.45c1.29 1.07 3.25 1.94 5 1.95zm-6.17 5c-.41 1.16-1.52 2-2.83 2-1.66 0-3-1.34-3-3 0-1.31.84-2.41 2-2.83V12.1c-2.28.46-4 2.48-4 4.9 0 2.76 2.24 5 5 5 2.42 0 4.44-1.72 4.9-4h-2.07z"/>',account_balance:'<path d="M4 10v7h3v-7H4z"/><path d="M10 10v7h3v-7h-3z"/><path d="M2 22h19v-3H2v3z"/><path d="M16 10v7h3v-7h-3z"/><path d="M11.5 1L2 6v2h19V6l-9.5-5z"/>',account_balance_wallet:'<path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9z"/><path d="M16 13.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM12 16h10V8H12v8z"/>',account_box:'<path d="M3 5v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.11 0-2 .9-2 2zm12 4c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3zm-9 8c0-2 4-3.1 6-3.1s6 1.1 6 3.1v1H6v-1z"/>',account_circle:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>',add_shopping_cart:'<path d="M11 9h2V6h3V4h-3V1h-2v3H8v2h3v3z"/><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2z"/><path d="M17 18c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/><path d="M7.17 14.75l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.86-7.01L19.42 4h-.01l-1.1 2-2.76 5H8.53l-.13-.27L6.16 6l-.95-2-.94-2H1v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.13 0-.25-.11-.25-.25z"/>',alarm:'<path d="M22 5.72l-4.6-3.86-1.29 1.53 4.6 3.86L22 5.72z"/><path d="M7.88 3.39L6.6 1.86 2 5.71l1.29 1.53 4.59-3.85z"/><path d="M12.5 8H11v6l4.75 2.85.75-1.23-4-2.37V8z"/><path d="M12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm0-16c-4.97 0-9 4.03-9 9s4.02 9 9 9c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/>',alarm_add:'<path d="M7.88 3.39L6.6 1.86 2 5.71l1.29 1.53 4.59-3.85z"/><path d="M22 5.72l-4.6-3.86-1.29 1.53 4.6 3.86L22 5.72z"/><path d="M12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm0-16c-4.97 0-9 4.03-9 9s4.02 9 9 9c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/><path d="M13 9h-2v3H8v2h3v3h2v-3h3v-2h-3V9z"/>',alarm_off:'<path d="M12 6c3.87 0 7 3.13 7 7 0 .84-.16 1.65-.43 2.4l1.52 1.52c.58-1.19.91-2.51.91-3.92a9 9 0 0 0-9-9c-1.41 0-2.73.33-3.92.91L9.6 6.43C10.35 6.16 11.16 6 12 6z"/><path d="M22 5.72l-4.6-3.86-1.29 1.53 4.6 3.86L22 5.72z"/><path d="M16.47 18.39C15.26 19.39 13.7 20 12 20c-3.87 0-7-3.13-7-7 0-1.7.61-3.26 1.61-4.47l9.86 9.86zM2.92 2.29L1.65 3.57 2.98 4.9l-1.11.93 1.42 1.42 1.11-.94.8.8A8.964 8.964 0 0 0 3 13c0 4.97 4.02 9 9 9 2.25 0 4.31-.83 5.89-2.2l2.2 2.2 1.27-1.27L3.89 3.27l-.97-.98z"/><path d="M8.02 3.28L6.6 1.86l-.86.71 1.42 1.42.86-.71z"/>',alarm_on:'<path d="M22 5.72l-4.6-3.86-1.29 1.53 4.6 3.86L22 5.72z"/><path d="M7.88 3.39L6.6 1.86 2 5.71l1.29 1.53 4.59-3.85z"/><path d="M12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm0-16c-4.97 0-9 4.03-9 9s4.02 9 9 9c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/><path d="M10.54 14.53L8.41 12.4l-1.06 1.06 3.18 3.18 6-6-1.06-1.06-4.93 4.95z"/>',all_out:'<path d="M16.21 4.16l4 4v-4z"/><path d="M20.21 16.16l-4 4h4z"/><path d="M8.21 20.16l-4-4v4z"/><path d="M4.21 8.16l4-4h-4z"/><path d="M16.06 16.01a5.438 5.438 0 0 1-7.7 0 5.438 5.438 0 0 1 0-7.7 5.438 5.438 0 0 1 7.7 0 5.438 5.438 0 0 1 0 7.7zm1.1-8.8a7.007 7.007 0 0 0-9.9 0 7.007 7.007 0 0 0 0 9.9 7.007 7.007 0 0 0 9.9 0c2.73-2.73 2.73-7.16 0-9.9z"/>',android:'<path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10z"/><path d="M3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8z"/><path d="M20.5 8c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5z"/><path d="M15 5h-1V4h1v1zm-5 0H9V4h1v1zm5.53-2.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84z"/>',announcement:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 9h-2V5h2v6zm0 4h-2v-2h2v2z"/>',aspect_ratio:'<path d="M19 12h-2v3h-3v2h5v-5z"/><path d="M7 9h3V7H5v5h2V9z"/><path d="M21 19.01H3V4.99h18v14.02zM21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',assessment:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>',assignment:'<path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>',assignment_ind:'<path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0 4c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm6 12H6v-1.4c0-2 4-3.1 6-3.1s6 1.1 6 3.1V19z"/>',assignment_late:'<path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-6 15h-2v-2h2v2zm0-4h-2V8h2v6zm-1-9c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>',assignment_return:'<path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm4 12h-4v3l-5-5 5-5v3h4v4z"/>',assignment_returned:'<path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0 15l-5-5h3V9h4v4h3l-5 5z"/>',assignment_turned_in:'<path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>',autorenew:'<path d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6z"/><path d="M18.76 7.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"/>',backup:'<path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>',book:'<path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z"/>',bookmark:'<path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z"/>',bookmark_outline:'<path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2zm0 15l-5-2.18L7 18V5h10v13z"/>',bug_report:'<path d="M20 8h-2.81c-.45-.78-1.07-1.45-1.82-1.96L17 4.41 15.59 3l-2.17 2.17C12.96 5.06 12.49 5 12 5c-.49 0-.96.06-1.41.17L8.41 3 7 4.41l1.62 1.63C7.88 6.55 7.26 7.22 6.81 8H4v2h2.09c-.05.33-.09.66-.09 1v1H4v2h2v1c0 .34.04.67.09 1H4v2h2.81c1.04 1.79 2.97 3 5.19 3s4.15-1.21 5.19-3H20v-2h-2.09c.05-.33.09-.66.09-1v-1h2v-2h-2v-1c0-.34-.04-.67-.09-1H20V8zm-6 8h-4v-2h4v2zm0-4h-4v-2h4v2z"/>',build:'<path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>',cached:'<path d="M19 8l-4 4h3c0 3.31-2.69 6-6 6-1.01 0-1.97-.25-2.8-.7l-1.46 1.46C8.97 19.54 10.43 20 12 20c4.42 0 8-3.58 8-8h3l-4-4z"/><path d="M6 12c0-3.31 2.69-6 6-6 1.01 0 1.97.25 2.8.7l1.46-1.46C15.03 4.46 13.57 4 12 4c-4.42 0-8 3.58-8 8H1l4 4 4-4H6z"/>',camera_enhanced:'<path d="M9 3L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2h-3.17L15 3H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/> <path d="M12 17l1.25-2.75L16 13l-2.75-1.25L12 9l-1.25 2.75L8 13l2.75 1.25z"/>',card_giftcard:'<path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/>',card_membership:'<path d="M20 2H4c-1.11 0-2 .89-2 2v11c0 1.11.89 2 2 2h4v5l4-2 4 2v-5h4c1.11 0 2-.89 2-2V4c0-1.11-.89-2-2-2zm0 13H4v-2h16v2zm0-5H4V4h16v6z"/>',card_travel:'<path d="M20 6h-3V4c0-1.11-.89-2-2-2H9c-1.11 0-2 .89-2 2v2H4c-1.11 0-2 .89-2 2v11c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zM9 4h6v2H9V4zm11 15H4v-2h16v2zm0-5H4V8h3v2h2V8h6v2h2V8h3v6z"/>',change_history:'<path d="M12 7.77L18.39 18H5.61L12 7.77M12 4L2 20h20L12 4z"/>',check_circle:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>',chrome_reader_mode:'<path d="M13 12h7v1.5h-7z"/><path d="M13 9.5h7V11h-7z"/><path d="M13 14.5h7V16h-7z"/><path d="M21 19h-9V6h9v13zm0-15H3c-1.1 0-2 .9-2 2v13c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z"/>',"class":'<path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z"/>',code:'<path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4z"/><path d="M14.6 16.6l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>',compare_arrows:'<path d="M9.01 14H2v2h7.01v3L13 15l-3.99-4v3z"/><path d="M14.99 13v-3H22V8h-7.01V5L11 9l3.99 4z"/>',copyright:'<path d="M10.08 10.86c.05-.33.16-.62.3-.87.14-.25.34-.46.59-.62.24-.15.54-.22.91-.23.23.01.44.05.63.13.2.09.38.21.52.36s.25.33.34.53c.09.2.13.42.14.64h1.79c-.02-.47-.11-.9-.28-1.29-.17-.39-.4-.73-.7-1.01-.3-.28-.66-.5-1.08-.66-.42-.16-.88-.23-1.39-.23-.65 0-1.22.11-1.7.34-.48.23-.88.53-1.2.92-.32.39-.56.84-.71 1.36-.15.52-.24 1.06-.24 1.64v.27c0 .58.08 1.12.23 1.64.15.52.39.97.71 1.35.32.38.72.69 1.2.91.48.22 1.05.34 1.7.34.47 0 .91-.08 1.32-.23.41-.15.77-.36 1.08-.63.31-.27.56-.58.74-.94.18-.36.29-.74.3-1.15h-1.79c-.01.21-.06.4-.15.58-.09.18-.21.33-.36.46s-.32.23-.52.3c-.19.07-.39.09-.6.1-.36-.01-.66-.08-.89-.23a1.75 1.75 0 0 1-.59-.62c-.14-.25-.25-.55-.3-.88a6.74 6.74 0 0 1-.08-1v-.27c0-.35.03-.68.08-1.01z"/><path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>',credit_card:'<path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>',dashboard:'<path d="M3 13h8V3H3v10z"/><path d="M3 21h8v-6H3v6z"/><path d="M13 21h8V11h-8v10z"/><path d="M13 3v6h8V3h-8z"/>',date_range:'<path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>',"delete":'<path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12z"/><path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>',delete_forever:'<path d="M8.46 11.88l1.41-1.41L12 12.59l2.12-2.12 1.41 1.41L13.41 14l2.12 2.12-1.41 1.41L12 15.41l-2.12 2.12-1.41-1.41L10.59 14l-2.13-2.12zM6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12z"/><path d="M15.5 4l-1-1h-5l-1 1H5v2h14V4z"/>',description:'<path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>',dns:'<path d="M7 19c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm13-6H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1z"/><path d="M7 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm13-6H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1z"/>',done:'<path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>',done_all:'<path d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7z"/><path d="M22.24 5.59L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41z"/><path d="M.41 13.41L6 19l1.41-1.41L1.83 12 .41 13.41z"/>',donut_large:'<path d="M11 5.08V2c-5 .5-9 4.81-9 10s4 9.5 9 10v-3.08c-3-.48-6-3.4-6-6.92s3-6.44 6-6.92z"/><path d="M18.97 11H22c-.47-5-4-8.53-9-9v3.08C16 5.51 18.54 8 18.97 11z"/><path d="M13 18.92V22c5-.47 8.53-4 9-9h-3.03c-.43 3-2.97 5.49-5.97 5.92z"/>',donut_small:'<path d="M11 9.16V2c-5 .5-9 4.79-9 10s4 9.5 9 10v-7.16c-1-.41-2-1.52-2-2.84 0-1.32 1-2.43 2-2.84z"/><path d="M14.86 11H22c-.48-4.75-4-8.53-9-9v7.16c1 .3 1.52.98 1.86 1.84z"/><path d="M13 14.84V22c5-.47 8.52-4.25 9-9h-7.14c-.34.86-.86 1.54-1.86 1.84z"/>',eject:'<path d="M5 17h14v2H5z"/><path d="M12 5L5.33 15h13.34z"/>',euro_symbol:'<path d="M15 18.5c-2.51 0-4.68-1.42-5.76-3.5H15v-2H8.58c-.05-.33-.08-.66-.08-1s.03-.67.08-1H15V9H9.24C10.32 6.92 12.5 5.5 15 5.5c1.61 0 3.09.59 4.23 1.57L21 5.3C19.41 3.87 17.3 3 15 3c-3.92 0-7.24 2.51-8.48 6H3v2h3.06c-.04.33-.06.66-.06 1 0 .34.02.67.06 1H3v2h3.52c1.24 3.49 4.56 6 8.48 6 2.31 0 4.41-.87 6-2.3l-1.78-1.77c-1.13.98-2.6 1.57-4.22 1.57z"/>',event:'<path d="M17 12h-5v5h5v-5z"/><path d="M19 19H5V8h14v11zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2z"/>',event_seat:'<path d="M4 18v3h3v-3h10v3h3v-6H4z"/><path d="M19 10h3v3h-3z"/><path d="M2 10h3v3H2z"/><path d="M17 13H7V5c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v8z"/>',exit_to_app:'<path d="M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59z"/><path d="M19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',explore:'<path d="M12 10.9c-.61 0-1.1.49-1.1 1.1 0 .61.49 1.1 1.1 1.1.61 0 1.1-.49 1.1-1.1 0-.61-.49-1.1-1.1-1.1z"/><path d="M14.19 14.19L6 18l3.81-8.19L18 6l-3.81 8.19zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>',extension:'<path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7 1.49 0 2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z"/>',face:'<path d="M14.69 17.1c-.74.58-1.7.9-2.69.9s-1.95-.32-2.69-.9c-.22-.17-.53-.13-.7.09-.17.22-.13.53.09.7.91.72 2.09 1.11 3.3 1.11s2.39-.39 3.31-1.1c.22-.17.26-.48.09-.7-.17-.23-.49-.26-.71-.1z"/><path d="M19.96 14.82c-1.09 3.74-4.27 6.46-8.04 6.46-3.78 0-6.96-2.72-8.04-6.47-1.19-.11-2.13-1.18-2.13-2.52 0-1.27.85-2.31 1.97-2.5 2.09-1.46 3.8-3.49 4.09-5.05v-.01c1.35 2.63 6.3 5.19 11.83 5.06l.3-.03c1.28 0 2.31 1.14 2.31 2.54 0 1.38-1.02 2.51-2.29 2.52zM12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z"/><path d="M16.5 12.5c0 .552-.448 1-1 1s-1-.448-1-1 .448-1 1-1 1 .448 1 1zm-7 0c0 .552-.448 1-1 1s-1-.448-1-1 .448-1 1-1 1 .448 1 1z"/>',favorite:'<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>',favorite_border:'<path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z"/>',feedback:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z"/>',find_in_page:'<path d="M20 19.59V8l-6-6H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c.45 0 .85-.15 1.19-.4l-4.43-4.43c-.8.52-1.74.83-2.76.83-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5c0 1.02-.31 1.96-.83 2.75L20 19.59z"/><path d="M9 13c0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3-3 1.34-3 3z"/>',
find_replace:'<path d="M11 6c1.38 0 2.63.56 3.54 1.46L12 10h6V4l-2.05 2.05C14.68 4.78 12.93 4 11 4c-3.53 0-6.43 2.61-6.92 6H6.1c.46-2.28 2.48-4 4.9-4z"/><path d="M16.64 15.14c.66-.9 1.12-1.97 1.28-3.14H15.9c-.46 2.28-2.48 4-4.9 4-1.38 0-2.63-.56-3.54-1.46L10 12H4v6l2.05-2.05C7.32 17.22 9.07 18 11 18c1.55 0 2.98-.51 4.14-1.36L20 21.49 21.49 20l-4.85-4.86z"/>',fingerprint:'<path d="M17.81 4.47c-.08 0-.16-.02-.23-.06C15.66 3.42 14 3 12.01 3c-1.98 0-3.86.47-5.57 1.41-.24.13-.54.04-.68-.2a.506.506 0 0 1 .2-.68C7.82 2.52 9.86 2 12.01 2c2.13 0 3.99.47 6.03 1.52.25.13.34.43.21.67a.49.49 0 0 1-.44.28z"/><path d="M3.5 9.72a.499.499 0 0 1-.41-.79c.99-1.4 2.25-2.5 3.75-3.27C9.98 4.04 14 4.03 17.15 5.65c1.5.77 2.76 1.86 3.75 3.25a.5.5 0 0 1-.12.7c-.23.16-.54.11-.7-.12a9.388 9.388 0 0 0-3.39-2.94c-2.87-1.47-6.54-1.47-9.4.01-1.36.7-2.5 1.7-3.4 2.96-.08.14-.23.21-.39.21z"/><path d="M9.75 21.79a.47.47 0 0 1-.35-.15c-.87-.87-1.34-1.43-2.01-2.64-.69-1.23-1.05-2.73-1.05-4.34 0-2.97 2.54-5.39 5.66-5.39s5.66 2.42 5.66 5.39c0 .28-.22.5-.5.5s-.5-.22-.5-.5c0-2.42-2.09-4.39-4.66-4.39-2.57 0-4.66 1.97-4.66 4.39 0 1.44.32 2.77.93 3.85.64 1.15 1.08 1.64 1.85 2.42.19.2.19.51 0 .71-.11.1-.24.15-.37.15z"/><path d="M16.92 19.94c-1.19 0-2.24-.3-3.1-.89-1.49-1.01-2.38-2.65-2.38-4.39 0-.28.22-.5.5-.5s.5.22.5.5c0 1.41.72 2.74 1.94 3.56.71.48 1.54.71 2.54.71.24 0 .64-.03 1.04-.1.27-.05.53.13.58.41.05.27-.13.53-.41.58-.57.11-1.07.12-1.21.12z"/><path d="M14.91 22c-.04 0-.09-.01-.13-.02-1.59-.44-2.63-1.03-3.72-2.1a7.297 7.297 0 0 1-2.17-5.22c0-1.62 1.38-2.94 3.08-2.94 1.7 0 3.08 1.32 3.08 2.94 0 1.07.93 1.94 2.08 1.94s2.08-.87 2.08-1.94c0-3.77-3.25-6.83-7.25-6.83-2.84 0-5.44 1.58-6.61 4.03-.39.81-.59 1.76-.59 2.8 0 .78.07 2.01.67 3.61.1.26-.03.55-.29.64-.26.1-.55-.04-.64-.29a11.14 11.14 0 0 1-.73-3.96c0-1.2.23-2.29.68-3.24 1.33-2.79 4.28-4.6 7.51-4.6 4.55 0 8.25 3.51 8.25 7.83 0 1.62-1.38 2.94-3.08 2.94-1.7 0-3.08-1.32-3.08-2.94 0-1.07-.93-1.94-2.08-1.94s-2.08.87-2.08 1.94c0 1.71.66 3.31 1.87 4.51.95.94 1.86 1.46 3.27 1.85.27.07.42.35.35.61-.05.23-.26.38-.47.38z"/>',flight_land:'<path d="M2.5 19h19v2h-19z"/><path d="M9.68 13.27l4.35 1.16 5.31 1.42c.8.21 1.62-.26 1.84-1.06.21-.8-.26-1.62-1.06-1.84l-5.31-1.42-2.76-9.02L10.12 2v8.28L5.15 8.95l-.93-2.32-1.45-.39v5.17l1.6.43 5.31 1.43z"/>',flight_takeoff:'<path d="M2.5 19h19v2h-19z"/><path d="M22.07 9.64c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 1.82 3.16.77 1.33 1.6-.43 5.31-1.42 4.35-1.16L21 11.49c.81-.23 1.28-1.05 1.07-1.85z"/>',flip_to_back:'<path d="M9 7H7v2h2V7z"/><path d="M9 11H7v2h2v-2z"/><path d="M9 3a2 2 0 0 0-2 2h2V3z"/><path d="M13 15h-2v2h2v-2z"/><path d="M19 3v2h2c0-1.1-.9-2-2-2z"/><path d="M13 3h-2v2h2V3z"/><path d="M9 17v-2H7a2 2 0 0 0 2 2z"/><path d="M19 13h2v-2h-2v2z"/><path d="M19 9h2V7h-2v2z"/><path d="M19 17c1.1 0 2-.9 2-2h-2v2z"/><path d="M5 7H3v12a2 2 0 0 0 2 2h12v-2H5V7z"/><path d="M15 5h2V3h-2v2z"/><path d="M15 17h2v-2h-2v2z"/>',flip_to_front:'<path d="M3 13h2v-2H3v2z"/><path d="M3 17h2v-2H3v2z"/><path d="M5 21v-2H3a2 2 0 0 0 2 2z"/><path d="M3 9h2V7H3v2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M19 15H9V5h10v10zm0-12H9a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M11 21h2v-2h-2v2z"/><path d="M7 21h2v-2H7v2z"/>',g_translate:'<path d="M20 5h-9.12L10 2H4c-1.1 0-2 .9-2 2v13c0 1.1.9 2 2 2h7l1 3h8c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zM7.17 14.59c-2.25 0-4.09-1.83-4.09-4.09s1.83-4.09 4.09-4.09c1.04 0 1.99.37 2.74 1.07l.07.06-1.23 1.18-.06-.05c-.29-.27-.78-.59-1.52-.59-1.31 0-2.38 1.09-2.38 2.42s1.07 2.42 2.38 2.42c1.37 0 1.96-.87 2.12-1.46H7.08V9.91h3.95l.01.07c.04.21.05.4.05.61 0 2.35-1.61 4-3.92 4zm6.03-1.71c.33.6.74 1.18 1.19 1.7l-.54.53-.65-2.23zm.77-.76h-.99l-.31-1.04h3.99s-.34 1.31-1.56 2.74c-.52-.62-.89-1.23-1.13-1.7zM21 20c0 .55-.45 1-1 1h-7l2-2-.81-2.77.92-.92L17.79 18l.73-.73-2.71-2.68c.9-1.03 1.6-2.25 1.92-3.51H19v-1.04h-3.64V9h-1.04v1.04h-1.96L11.18 6H20c.55 0 1 .45 1 1v13z"/>',gavel:'<path d="M1 21h12v2H1z"/><path d="M5.245 8.07l2.83-2.827 14.14 14.142-2.828 2.828z"/><path d="M12.317 1l5.657 5.656-2.83 2.83-5.654-5.66z"/><path d="M3.825 9.485l5.657 5.657-2.828 2.828-5.657-5.657z"/>',get_app:'<path d="M19 9h-4V3H9v6H5l7 7 7-7z"/><path d="M5 18v2h14v-2H5z"/>',gif:'<path d="M11.5 9H13v6h-1.5z"/><path d="M9 9H6c-.6 0-1 .5-1 1v4c0 .5.4 1 1 1h3c.6 0 1-.5 1-1v-2H8.5v1.5h-2v-3H10V10c0-.5-.4-1-1-1z"/><path d="M19 10.5V9h-4.5v6H16v-2h2v-1.5h-2v-1z"/>',grade:'<path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>',group_work:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM8 17.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM9.5 8c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8zm6.5 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>',help:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>',help_outline:'<path d="M11 18h2v-2h-2v2z"/><path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M12 6c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"/>',highlight_off:'<path d="M14.59 8L12 10.59 9.41 8 8 9.41 10.59 12 8 14.59 9.41 16 12 13.41 14.59 16 16 14.59 13.41 12 16 9.41 14.59 8z"/><path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2z"/>',history:'<path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/><path d="M12 8v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>',home:'<path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>',hourglass_empty:'<path d="M6 2v6h.01L6 8.01 10 12l-4 4 .01.01H6V22h12v-5.99h-.01L18 16l-4-4 4-3.99-.01-.01H18V2H6zm10 14.5V20H8v-3.5l4-4 4 4zm-4-5l-4-4V4h8v3.5l-4 4z"/>',hourglass_full:'<path d="M6 2v6h.01L6 8.01 10 12l-4 4 .01.01H6V22h12v-5.99h-.01L18 16l-4-4 4-3.99-.01-.01H18V2H6z"/>',http:'<path d="M4.5 11h-2V9H1v6h1.5v-2.5h2V15H6V9H4.5v2z"/><path d="M7 10.5h1.5V15H10v-4.5h1.5V9H7v1.5z"/><path d="M12.5 10.5H14V15h1.5v-4.5H17V9h-4.5v1.5z"/><path d="M21.5 11.5h-2v-1h2v1zm0-2.5H18v6h1.5v-2h2c.8 0 1.5-.7 1.5-1.5v-1c0-.8-.7-1.5-1.5-1.5z"/>',https:'<path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>',important_devices:'<path d="M23 20h-5v-7h5v7zm0-8.99L18 11c-.55 0-1 .45-1 1v9c0 .55.45 1 1 1h5c.55 0 1-.45 1-1v-9c0-.55-.45-.99-1-.99z"/><path d="M20 2H2C.89 2 0 2.89 0 4v12a2 2 0 0 0 2 2h7v2H7v2h8v-2h-2v-2h2v-2H2V4h18v5h2V4a2 2 0 0 0-2-2z"/><path d="M11.97 9L11 6l-.97 3H7l2.47 1.76-.94 2.91 2.47-1.8 2.47 1.8-.94-2.91L15 9h-3.03z"/>',info:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>',info_outline:'<path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M11 9h2V7h-2v2zm0 8h2v-6h-2v6z"/>',input:'<path d="M21 3.01H3c-1.1 0-2 .9-2 2V9h2V4.99h18v14.03H3V15H1v4.01c0 1.1.9 1.98 2 1.98h18c1.1 0 2-.88 2-1.98v-14c0-1.11-.9-2-2-2z"/><path d="M11 16l4-4-4-4v3H1v2h10v3z"/>',invert_colors:'<path d="M17.66 7.93L12 2.27 6.34 7.93c-3.12 3.12-3.12 8.19 0 11.31C7.9 20.8 9.95 21.58 12 21.58c2.05 0 4.1-.78 5.66-2.34 3.12-3.12 3.12-8.19 0-11.31zM12 19.59c-1.6 0-3.11-.62-4.24-1.76C6.62 16.69 6 15.19 6 13.59s.62-3.11 1.76-4.24L12 5.1v14.49z"/>',label:'<path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z"/>',label_outline:'<path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16zM16 17H5V7h11l3.55 5L16 17z"/>',language:'<path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z"/>',launch:'<path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z"/><path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>',lightbulb_outline:'<path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1z"/><path d="M14.85 13.1l-.85.6V16h-4v-2.3l-.85-.6A4.997 4.997 0 0 1 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1zM12 2C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z"/>',line_style:'<path d="M3 16h5v-2H3v2z"/><path d="M9.5 16h5v-2h-5v2z"/><path d="M16 16h5v-2h-5v2z"/><path d="M3 20h2v-2H3v2z"/><path d="M7 20h2v-2H7v2z"/><path d="M11 20h2v-2h-2v2z"/><path d="M15 20h2v-2h-2v2z"/><path d="M19 20h2v-2h-2v2z"/><path d="M3 12h8v-2H3v2z"/><path d="M13 12h8v-2h-8v2z"/><path d="M3 4v4h18V4H3z"/>',line_weight:'<path d="M3 17h18v-2H3v2z"/><path d="M3 20h18v-1H3v1z"/><path d="M3 13h18v-3H3v3z"/><path d="M3 4v4h18V4H3z"/>',list:'<path d="M3 9h2V7H3v2zm0 8h2v-2H3v2zm0-4h2v-2H3v2z"/><path d="M7 13h14v-2H7v2z"/><path d="M7 17h14v-2H7v2z"/><path d="M7 7v2h14V7H7z"/>',lock:'<path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>',lock_open:'<path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/><path d="M18 20H6V10h12v10zm0-12h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2z"/>',lock_outline:'<path d="M18 20H6V10h12zM12 2.9c1.71 0 3.1 1.39 3.1 3.1v2H9V6l-.002-.008C8.998 4.282 10.29 2.9 12 2.9zM18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2z"/><path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>',loyalty:'<path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7zm11.77 8.27L13 19.54l-4.27-4.27C8.28 14.81 8 14.19 8 13.5c0-1.38 1.12-2.5 2.5-2.5.69 0 1.32.28 1.77.74l.73.72.73-.73c.45-.45 1.08-.73 1.77-.73 1.38 0 2.5 1.12 2.5 2.5 0 .69-.28 1.32-.73 1.77z"/>',markunread_mailbox:'<path d="M20 6H10v6H8V4h6V0H6v6H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z"/>',motorcycle:'<path d="M19.44 9.03L15.41 5H11v2h3.59l2 2H5c-2.8 0-5 2.2-5 5s2.2 5 5 5c2.46 0 4.45-1.69 4.9-4h1.65l2.77-2.77c-.21.54-.32 1.14-.32 1.77 0 2.8 2.2 5 5 5s5-2.2 5-5c0-2.65-1.97-4.77-4.56-4.97zM7.82 15C7.4 16.15 6.28 17 5 17c-1.63 0-3-1.37-3-3s1.37-3 3-3c1.28 0 2.4.85 2.82 2H5v2h2.82zM19 17c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>',note_add:'<path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 14h-3v3h-2v-3H8v-2h3v-3h2v3h3v2zm-3-7V3.5L18.5 9H13z"/>',offline_pin:'<path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm5 16H7v-2h10v2zm-6.7-4L7 10.7l1.4-1.4 1.9 1.9 5.3-5.3L17 7.3 10.3 14z"/>',opacity:'<path d="M17.66 8L12 2.35 6.34 8C4.78 9.56 4 11.64 4 13.64s.78 4.11 2.34 5.67 3.61 2.35 5.66 2.35 4.1-.79 5.66-2.35S20 15.64 20 13.64 19.22 9.56 17.66 8zM6 14c.01-2 .62-3.27 1.76-4.4L12 5.27l4.24 4.38C17.38 10.77 17.99 12 18 14H6z"/>',open_in_browser:'<path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h4v-2H5V8h14v10h-4v2h4c1.1 0 2-.9 2-2V6c0-1.1-.89-2-2-2z"/><path d="M12 10l-4 4h3v6h2v-6h3l-4-4z"/>',open_in_new:'<path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z"/><path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>',open_with:'<path d="M10 9h4V6h3l-5-5-5 5h3v3z"/><path d="M9 10H6V7l-5 5 5 5v-3h3v-4z"/><path d="M23 12l-5-5v3h-3v4h3v3l5-5z"/><path d="M14 15h-4v3H7l5 5 5-5h-3v-3z"/>',pageview:'<path d="M11 8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/><path d="M17.59 19l-3.83-3.83c-.8.52-1.74.83-2.76.83-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5c0 1.02-.31 1.96-.83 2.75L19 17.59 17.59 19zM19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',pan_tool:'<path d="M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.08 0-2.1-.43-2.85-1.19L1 14.83s1.26-1.23 1.3-1.25c.22-.19.49-.29.79-.29.22 0 .42.06.6.16.04.01 4.31 2.46 4.31 2.46V4c0-.83.67-1.5 1.5-1.5S11 3.17 11 4v7h1V1.5c0-.83.67-1.5 1.5-1.5S15 .67 15 1.5V11h1V2.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h1V5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5z"/>',payment:'<path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>',perm_camera_mic:'<path d="M20 5h-3.17L15 3H9L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h7v-2.09c-2.83-.48-5-2.94-5-5.91h2c0 2.21 1.79 4 4 4s4-1.79 4-4h2c0 2.97-2.17 5.43-5 5.91V21h7c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-6 8c0 1.1-.9 2-2 2s-2-.9-2-2V9c0-1.1.9-2 2-2s2 .9 2 2v4z"/>',perm_contact_calendar:'<path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm6 12H6v-1c0-2 4-3.1 6-3.1s6 1.1 6 3.1v1z"/>',perm_data_setting:'<path d="M18.99 11.5c.34 0 .67.03 1 .07L20 0 0 20h11.56c-.04-.33-.07-.66-.07-1 0-4.14 3.36-7.5 7.5-7.5z"/><path d="M18.99 20.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm3.71-1.01c.02-.16.04-.32.04-.49 0-.17-.01-.33-.04-.49l1.06-.83c.09-.08.12-.21.06-.32l-1-1.73c-.06-.11-.19-.15-.31-.11l-1.24.5c-.26-.2-.54-.37-.85-.49l-.19-1.32c-.01-.12-.12-.21-.24-.21h-2c-.12 0-.23.09-.25.21l-.19 1.32c-.3.13-.59.29-.85.49l-1.24-.5c-.11-.04-.24 0-.31.11l-1 1.73c-.06.11-.04.24.06.32l1.06.83c-.02.16-.03.32-.03.49 0 .17.01.33.03.49l-1.06.83c-.09.08-.12.21-.06.32l1 1.73c.06.11.19.15.31.11l1.24-.5c.26.2.54.37.85.49l.19 1.32c.02.12.12.21.25.21h2c.12 0 .23-.09.25-.21l.19-1.32c.3-.13.59-.29.84-.49l1.25.5c.11.04.24 0 .31-.11l1-1.73c.06-.11.03-.24-.06-.32l-1.07-.83z"/>',perm_device_information:'<path d="M13 11h-2v6h2v-6zm0-4h-2v2h2V7z"/><path d="M17 19H7V5h10v14zm0-17.99L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99z"/>',perm_identity:'<path d="M12 4C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 1.9c1.16 0 2.1.94 2.1 2.1 0 1.16-.94 2.1-2.1 2.1-1.16 0-2.1-.94-2.1-2.1 0-1.16.94-2.1 2.1-2.1"/><path d="M12 13c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4zm0 1.9c2.97 0 6.1 1.46 6.1 2.1v1.1H5.9V17c0-.64 3.13-2.1 6.1-2.1"/>',perm_media:'<path d="M2 6H0v5h.01L0 20c0 1.1.9 2 2 2h18v-2H2V6z"/><path d="M7 15l4.5-6 3.5 4.51 2.5-3.01L21 15H7zM22 4h-8l-2-2H6c-1.1 0-1.99.9-1.99 2L4 16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z"/>',perm_phone_msg:'<path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.58l2.2-2.21c.28-.27.36-.66.25-1.01C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"/><path d="M12 3v10l3-3h6V3h-9z"/>',perm_scan_wifi:'<path d="M12 3C6.95 3 3.15 4.85 0 7.23L12 22 24 7.25C20.85 4.87 17.05 3 12 3zm1 13h-2v-6h2v6zm-2-8V6h2v2h-2z"/>',pets:'<circle cx="4.5" cy="9.5" r="2.5"/> <circle cx="9" cy="5.5" r="2.5"/> <circle cx="15" cy="5.5" r="2.5"/> <circle cx="19.5" cy="9.5" r="2.5"/> <path d="M17.34 14.86c-.87-1.02-1.6-1.89-2.48-2.91-.46-.54-1.05-1.08-1.75-1.32-.11-.04-.22-.07-.33-.09-.25-.04-.52-.04-.78-.04s-.53 0-.79.05c-.11.02-.22.05-.33.09-.7.24-1.28.78-1.75 1.32-.87 1.02-1.6 1.89-2.48 2.91-1.31 1.31-2.92 2.76-2.62 4.79.29 1.02 1.02 2.03 2.33 2.32.73.15 3.06-.44 5.54-.44h.18c2.48 0 4.81.58 5.54.44 1.31-.29 2.04-1.31 2.33-2.32.31-2.04-1.3-3.49-2.61-4.8z"/>',picture_in_picture:'<path d="M19 7h-8v6h8V7z"/><path d="M21 19.01H3V4.98h18v14.03zM21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 1.98 2 1.98h18c1.1 0 2-.88 2-1.98V5c0-1.1-.9-2-2-2z"/>',picture_in_picture_alt:'<path d="M19 11h-8v6h8v-6z"/><path d="M21 19.02H3V4.97h18v14.05zm2-.02V4.98C23 3.88 22.1 3 21 3H3c-1.1 0-2 .88-2 1.98V19c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2z"/>',play_for_work:'<path d="M11 5v5.59H7.5l4.5 4.5 4.5-4.5H13V5h-2z"/><path d="M6 14c0 3.31 2.69 6 6 6s6-2.69 6-6h-2c0 2.21-1.79 4-4 4s-4-1.79-4-4H6z"/>',polymer:'<path d="M19 4h-4L7.11 16.63 4.5 12 9 4H5L.5 12 5 20h4l7.89-12.63L19.5 12 15 20h4l4.5-8z"/>',power_settings_new:'<path d="M13 3h-2v10h2V3z"/><path d="M17.83 5.17l-1.42 1.42A6.92 6.92 0 0 1 19 12c0 3.87-3.13 7-7 7A6.995 6.995 0 0 1 7.58 6.58L6.17 5.17A8.932 8.932 0 0 0 3 12a9 9 0 0 0 18 0c0-2.74-1.23-5.18-3.17-6.83z"/>',pregnant_woman:'<path d="M9 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm7 9c-.01-1.34-.83-2.51-2-3 0-1.66-1.34-3-3-3s-3 1.34-3 3v7h2v5h3v-5h3v-4z"/>',print:'<path d="M19 12c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-3 7H8v-5h8v5zm3-11H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3z"/><path d="M18 3H6v4h12V3z"/>',query_builder:'<path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.01-18C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>',question_answer:'<path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1z"/><path d="M17 12V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z"/>',receipt:'<path d="M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z"/>',record_voice_over:'<circle cx="9" cy="9" r="4"/><path d="M9 15c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4zm7.76-9.64l-1.68 1.69c.84 1.18.84 2.71 0 3.89l1.68 1.69c2.02-2.02 2.02-5.07 0-7.27zM20.07 2l-1.63 1.63c2.77 3.02 2.77 7.56 0 10.74L20.07 16c3.9-3.89 3.91-9.95 0-14z"/>',redeem:'<path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/>',remove_shopping_cart:'<path d="M7.42 15c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h2.36l2 2H7.42zm15.31 7.73L2.77 2.77 2 2l-.73-.73L0 2.54l4.39 4.39 2.21 4.66-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h7.46l1.38 1.38A1.997 1.997 0 0 0 17 22c.67 0 1.26-.33 1.62-.84L21.46 24l1.27-1.27z"/><path d="M15.55 13c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0 0 20 4H6.54l9.01 9z"/><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2z"/>',reorder:'<path d="M3 15h18v-2H3v2z"/><path d="M3 19h18v-2H3v2z"/><path d="M3 11h18V9H3v2z"/><path d="M3 5v2h18V5H3z"/>',report_problem:'<path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>',restore:'<path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/><path d="M12 8v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>',restore_page:'<path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm-2 16c-2.05 0-3.81-1.24-4.58-3h1.71c.63.9 1.68 1.5 2.87 1.5 1.93 0 3.5-1.57 3.5-3.5S13.93 9.5 12 9.5c-1.35 0-2.52.78-3.1 1.9l1.6 1.6h-4V9l1.3 1.3C8.69 8.92 10.23 8 12 8c2.76 0 5 2.24 5 5s-2.24 5-5 5z"/>',room:'<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>',rounded_corner:'<path d="M19 19h2v2h-2v-2z"/><path d="M19 17h2v-2h-2v2z"/><path d="M3 13h2v-2H3v2z"/><path d="M3 17h2v-2H3v2z"/><path d="M3 9h2V7H3v2z"/><path d="M3 5h2V3H3v2z"/><path d="M7 5h2V3H7v2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M11 21h2v-2h-2v2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M7 21h2v-2H7v2z"/><path d="M3 21h2v-2H3v2z"/><path d="M21 8c0-2.76-2.24-5-5-5h-5v2h5c1.65 0 3 1.35 3 3v5h2V8z"/>',rowing:'<path d="M8.5 14.5L4 19l1.5 1.5L9 17h2l-2.5-2.5z"/><path d="M15 1c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M21 21.01L18 24l-2.99-3.01V19.5l-7.1-7.09c-.31.05-.61.07-.91.07v-2.16c1.66.03 3.61-.87 4.67-2.04l1.4-1.55c.19-.21.43-.38.69-.5.29-.14.62-.23.96-.23h.03C15.99 6.01 17 7.02 17 8.26v5.75c0 .84-.35 1.61-.92 2.16l-3.58-3.58v-2.27c-.63.52-1.43 1.02-2.29 1.39L16.5 18H18l3 3.01z"/>',schedule:'<path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.01-18C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>',search:'<path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>',settings:'<path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z"/>',settings_applications:'<path d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M17.25 12c0 .23-.02.46-.05.68l1.48 1.16c.13.11.17.3.08.45l-1.4 2.42c-.09.15-.27.21-.43.15l-1.74-.7c-.36.28-.76.51-1.18.69l-.26 1.85c-.03.17-.18.3-.35.3h-2.8c-.17 0-.32-.13-.35-.29l-.26-1.85c-.43-.18-.82-.41-1.18-.69l-1.74.7c-.16.06-.34 0-.43-.15l-1.4-2.42c-.09-.15-.05-.34.08-.45l1.48-1.16c-.03-.23-.05-.46-.05-.69 0-.23.02-.46.05-.68l-1.48-1.16c-.13-.11-.17-.3-.08-.45l1.4-2.42c.09-.15.27-.21.43-.15l1.74.7c.36-.28.76-.51 1.18-.69l.26-1.85c.03-.17.18-.3.35-.3h2.8c.17 0 .32.13.35.29l.26 1.85c.43.18.82.41 1.18.69l1.74-.7c.16-.06.34 0 .43.15l1.4 2.42c.09.15.05.34-.08.45l-1.48 1.16c.03.23.05.46.05.69zM19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2z"/>',settings_backup_restore:'<path d="M14 12c0-1.1-.9-2-2-2s-2 .9-2 2 .9 2 2 2 2-.9 2-2z"/><path d="M12 3c-4.97 0-9 4.03-9 9H0l4 4 4-4H5c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.51 0-2.91-.49-4.06-1.3l-1.42 1.44C8.04 20.3 9.94 21 12 21c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/>',settings_bluetooth:'<path d="M15 24h2v-2h-2v2zm-8 0h2v-2H7v2zm4 0h2v-2h-2v2z"/><path d="M14.88 14.29L13 16.17v-3.76l1.88 1.88zM13 3.83l1.88 1.88L13 7.59V3.83zm4.71 1.88L12 0h-1v7.59L6.41 3 5 4.41 10.59 10 5 15.59 6.41 17 11 12.41V20h1l5.71-5.71-4.3-4.29 4.3-4.29z"/>',settings_brightness:'<path d="M21 19.01H3V4.99h18v14.02zM21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M12 9c1.66 0 3 1.34 3 3s-1.34 3-3 3V9zm-4 7h2.5l1.5 1.5 1.5-1.5H16v-2.5l1.5-1.5-1.5-1.5V8h-2.5L12 6.5 10.5 8H8v2.5L6.5 12 8 13.5V16z"/>',settings_cell:'<path d="M15 24h2v-2h-2v2zm-4 0h2v-2h-2v2zm-4 0h2v-2H7v2z"/><path d="M16 16H8V4h8v12zM16 .01L8 0C6.9 0 6 .9 6 2v16c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V2c0-1.1-.9-1.99-2-1.99z"/>',settings_ethernet:'<path d="M7.77 6.76L6.23 5.48.82 12l5.41 6.52 1.54-1.28L3.42 12l4.35-5.24z"/><path d="M11 13h2v-2h-2v2zm6-2h-2v2h2v-2zM7 13h2v-2H7v2z"/><path d="M17.77 5.48l-1.54 1.28L20.58 12l-4.35 5.24 1.54 1.28L23.18 12l-5.41-6.52z"/>',settings_input_antenna:'<path d="M12 5c-3.87 0-7 3.13-7 7h2c0-2.76 2.24-5 5-5s5 2.24 5 5h2c0-3.87-3.13-7-7-7z"/><path d="M13 14.29c.88-.39 1.5-1.26 1.5-2.29 0-1.38-1.12-2.5-2.5-2.5S9.5 10.62 9.5 12c0 1.02.62 1.9 1.5 2.29v3.3L7.59 21 9 22.41l3-3 3 3L16.41 21 13 17.59v-3.3z"/><path d="M12 1C5.93 1 1 5.93 1 12h2c0-4.97 4.03-9 9-9s9 4.03 9 9h2c0-6.07-4.93-11-11-11z"/>',settings_input_component:'<path d="M1 16c0 1.3.84 2.4 2 2.82V23h2v-4.18C6.16 18.4 7 17.3 7 16v-2H1v2zM5 2c0-.55-.45-1-1-1s-1 .45-1 1v4H1v6h6V6H5V2z"/><path d="M13 2c0-.55-.45-1-1-1s-1 .45-1 1v4H9v6h6V6h-2V2zM9 16c0 1.3.84 2.4 2 2.82V23h2v-4.18c1.16-.41 2-1.51 2-2.82v-2H9v2z"/><path d="M17 16c0 1.3.84 2.4 2 2.82V23h2v-4.18c1.16-.41 2-1.51 2-2.82v-2h-6v2zm4-10V2c0-.55-.45-1-1-1s-1 .45-1 1v4h-2v6h6V6h-2z"/>',settings_input_composite:'<path d="M1 16c0 1.3.84 2.4 2 2.82V23h2v-4.18C6.16 18.4 7 17.3 7 16v-2H1v2zM5 2c0-.55-.45-1-1-1s-1 .45-1 1v4H1v6h6V6H5V2z"/><path d="M13 2c0-.55-.45-1-1-1s-1 .45-1 1v4H9v6h6V6h-2V2zM9 16c0 1.3.84 2.4 2 2.82V23h2v-4.18c1.16-.41 2-1.51 2-2.82v-2H9v2z"/><path d="M17 16c0 1.3.84 2.4 2 2.82V23h2v-4.18c1.16-.41 2-1.51 2-2.82v-2h-6v2zm4-10V2c0-.55-.45-1-1-1s-1 .45-1 1v4h-2v6h6V6h-2z"/>',settings_input_hdmi:'<path d="M18 7V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v3H5v6l3 6v3h8v-3l3-6V7h-1zM8 4h8v3h-2V5h-1v2h-2V5h-1v2H8V4z"/>',settings_input_svideo:'<path d="M15 6.5c0-.83-.67-1.5-1.5-1.5h-3C9.67 5 9 5.67 9 6.5S9.67 8 10.5 8h3c.83 0 1.5-.67 1.5-1.5z"/><path d="M8.5 15c-.83 0-1.5.67-1.5 1.5S7.67 18 8.5 18s1.5-.67 1.5-1.5S9.33 15 8.5 15zM8 11.5c0-.83-.67-1.5-1.5-1.5S5 10.67 5 11.5 5.67 13 6.5 13 8 12.33 8 11.5z"/><path d="M12 21c-4.96 0-9-4.04-9-9s4.04-9 9-9 9 4.04 9 9-4.04 9-9 9zm0-20C5.93 1 1 5.93 1 12s4.93 11 11 11 11-4.93 11-11S18.07 1 12 1z"/><path d="M15.5 15c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm2-5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/>',settings_overscan:'<path d="M14 16h-4l2.01 2.5L14 16zm-8-6l-2.5 2.01L6 14v-4zm12 0v4l2.5-1.99L18 10zm-5.99-4.5L10 8h4l-1.99-2.5z"/><path d="M21 19.01H3V4.99h18v14.02zM21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',settings_phone:'<path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.58l2.2-2.21c.28-.27.36-.66.25-1.01C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"/><path d="M19 9v2h2V9h-2zm-2 0h-2v2h2V9zm-4 0h-2v2h2V9z"/>',settings_power:'<path d="M13 2h-2v10h2V2z"/><path d="M16.56 4.44l-1.45 1.45C16.84 6.94 18 8.83 18 11c0 3.31-2.69 6-6 6s-6-2.69-6-6c0-2.17 1.16-4.06 2.88-5.12L7.44 4.44C5.36 5.88 4 8.28 4 11c0 4.42 3.58 8 8 8s8-3.58 8-8c0-2.72-1.36-5.12-3.44-6.56z"/><path d="M15 24h2v-2h-2v2zm-4 0h2v-2h-2v2zm-4 0h2v-2H7v2z"/>',settings_remote:'<path d="M12 15c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3-6H9c-.55 0-1 .45-1 1v12c0 .55.45 1 1 1h6c.55 0 1-.45 1-1V10c0-.55-.45-1-1-1z"/><path d="M7.05 6.05l1.41 1.41C9.37 6.56 10.62 6 12 6s2.63.56 3.54 1.46l1.41-1.41C15.68 4.78 13.93 4 12 4c-1.93 0-3.68.78-4.95 2.05z"/><path d="M12 0C8.96 0 6.21 1.23 4.22 3.22l1.41 1.41C7.26 3.01 9.51 2 12 2s4.74 1.01 6.36 2.64l1.41-1.41C17.79 1.23 15.04 0 12 0z"/>',settings_voice:'<path d="M12 13c1.66 0 2.99-1.34 2.99-3L15 4c0-1.66-1.34-3-3-3S9 2.34 9 4v6c0 1.66 1.34 3 3 3z"/><path d="M15 24h2v-2h-2v2zm-4 0h2v-2h-2v2zm-4 0h2v-2H7v2z"/><path d="M19 10h-1.7c0 3-2.54 5.1-5.3 5.1S6.7 13 6.7 10H5c0 3.41 2.72 6.23 6 6.72V20h2v-3.28c3.28-.49 6-3.31 6-6.72z"/>',shop:'<path d="M16 6V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H2v13c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6h-6zm-6-2h4v2h-4V4zM9 18V9l7.5 4L9 18z"/>',shop_two:'<path d="M3 9H1v11c0 1.11.89 2 2 2h14c1.11 0 2-.89 2-2H3V9z"/><path d="M12 15V8l5.5 3-5.5 4zm0-12h4v2h-4V3zm6 2V3c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H5v11c0 1.11.89 2 2 2h14c1.11 0 2-.89 2-2V5h-5z"/>',shopping_basket:'<path d="M17.21 9l-4.38-6.56c-.19-.28-.51-.42-.83-.42-.32 0-.64.14-.83.43L6.79 9H2c-.55 0-1 .45-1 1 0 .09.01.18.04.27l2.54 9.27c.23.84 1 1.46 1.92 1.46h13c.92 0 1.69-.62 1.93-1.46l2.54-9.27L23 10c0-.55-.45-1-1-1h-4.79zM9 9l3-4.4L15 9H9zm3 8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>',shopping_cart:'<path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2z"/><path d="M1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1z"/><path d="M17 18c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>',speaker_notes:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM8 14H6v-2h2v2zm0-3H6V9h2v2zm0-3H6V6h2v2zm7 6h-5v-2h5v2zm3-3h-8V9h8v2zm0-3h-8V6h8v2z"/>',speaker_notes_off:'<path d="M6 11V9l2 2H6zm2 3H6v-2h2v2zm2.54-3l-.54-.54L7.54 8 6 6.46 2.38 2.84 1.27 1.73 0 3l2.01 2.01L2 22l4-4h9l5.73 5.73L22 22.46 17.54 18l-7-7z"/><path d="M20 2H4.08L10 7.92V6h8v2h-7.92l1 1H18v2h-4.92l6.99 6.99C21.14 17.95 22 17.08 22 16V4c0-1.1-.9-2-2-2z"/>',spellcheck:'<path d="M6.43 11L8.5 5.48 10.57 11H6.43zm6.02 5h2.09L9.43 3H7.57L2.46 16h2.09l1.12-3h5.64l1.14 3z"/><path d="M21.59 11.59l-8.09 8.09L9.83 16l-1.41 1.41 5.09 5.09L23 13l-1.41-1.41z"/>',star_rate:'<path d="M12 14.3l3.71 2.7-1.42-4.36L18 10h-4.55L12 5.5 10.55 10H6l3.71 2.64L8.29 17z"/>',stars:'<path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm4.24 16L12 15.45 7.77 18l1.12-4.81-3.73-3.23 4.92-.42L12 5l1.92 4.53 4.92.42-3.73 3.23L16.23 18z"/>',store:'<path d="M20 4H4v2h16V4z"/><path d="M12 18H6v-4h6v4zm9-4v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1z"/>',subject:'<path d="M14 17H4v2h10v-2z"/><path d="M20 9H4v2h16V9z"/><path d="M4 15h16v-2H4v2z"/><path d="M4 5v2h16V5H4z"/>',supervisor_account:'<path d="M16.5 12c1.38 0 2.49-1.12 2.49-2.5S17.88 7 16.5 7C15.12 7 14 8.12 14 9.5s1.12 2.5 2.5 2.5z"/><path d="M9 11c1.66 0 2.99-1.34 2.99-3S10.66 5 9 5C7.34 5 6 6.34 6 8s1.34 3 3 3z"/><path d="M16.5 14c-1.83 0-5.5.92-5.5 2.75V19h11v-2.25c0-1.83-3.67-2.75-5.5-2.75z"/><path d="M9 13c-2.33 0-7 1.17-7 3.5V19h7v-2.25c0-.85.33-2.34 2.37-3.47C10.5 13.1 9.66 13 9 13z"/>',swap_horiz:'<path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3z"/><path d="M21 9l-3.99-4v3H10v2h7.01v3L21 9z"/>',swap_vert:'<path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3z"/><path d="M9 3L5 6.99h3V14h2V6.99h3L9 3z"/>',swap_vertial_circle:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM6.5 9L10 5.5 13.5 9H11v4H9V9H6.5zm11 6L14 18.5 10.5 15H13v-4h2v4h2.5z"/>',system_update_alt:'<path d="M12 16.5l4-4h-3v-9h-2v9H8l4 4z"/><path d="M21 3.5h-6v1.99h6v14.03H3V5.49h6V3.5H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2v-14c0-1.1-.9-2-2-2z"/>',tab:'<path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h10v4h8v10z"/>',tab_unselected:'<path d="M1 9h2V7H1v2z"/><path d="M1 13h2v-2H1v2z"/><path d="M1 5h2V3c-1.1 0-2 .9-2 2z"/><path d="M9 21h2v-2H9v2z"/><path d="M1 17h2v-2H1v2z"/><path d="M3 21v-2H1c0 1.1.9 2 2 2z"/><path d="M21 3h-8v6h10V5c0-1.1-.9-2-2-2z"/><path d="M21 17h2v-2h-2v2z"/><path d="M9 5h2V3H9v2z"/><path d="M5 21h2v-2H5v2z"/><path d="M5 5h2V3H5v2z"/><path d="M21 21c1.1 0 2-.9 2-2h-2v2z"/><path d="M21 13h2v-2h-2v2z"/><path d="M13 21h2v-2h-2v2z"/><path d="M17 21h2v-2h-2v2z"/>',
theaters:'<path d="M18 3v2h-2V3H8v2H6V3H4v18h2v-2h2v2h8v-2h2v2h2V3h-2zM8 17H6v-2h2v2zm0-4H6v-2h2v2zm0-4H6V7h2v2zm10 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z"/>',thumb_down:'<path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v1.91l.01.01L1 14c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2z"/><path d="M19 3v12h4V3h-4z"/>',thumb_up:'<path d="M1 21h4V9H1v12z"/><path d="M23 10c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-1.91l-.01-.01L23 10z"/>',thumbs_up_down:'<path d="M12 6c0-.55-.45-1-1-1H5.82l.66-3.18.02-.23c0-.31-.13-.59-.33-.8L5.38 0 .44 4.94C.17 5.21 0 5.59 0 6v6.5c0 .83.67 1.5 1.5 1.5h6.75c.62 0 1.15-.38 1.38-.91l2.26-5.29c.07-.17.11-.36.11-.55V6z"/><path d="M22.5 10h-6.75c-.62 0-1.15.38-1.38.91l-2.26 5.29c-.07.17-.11.36-.11.55V18c0 .55.45 1 1 1h5.18l-.66 3.18-.02.24c0 .31.13.59.33.8l.79.78 4.94-4.94c.27-.27.44-.65.44-1.06v-6.5c0-.83-.67-1.5-1.5-1.5z"/>',timeline:'<path d="M23 8c0 1.1-.9 2-2 2-.18 0-.35-.02-.51-.07l-3.56 3.55c.05.16.07.34.07.52 0 1.1-.9 2-2 2s-2-.9-2-2c0-.18.02-.36.07-.52l-2.55-2.55c-.16.05-.34.07-.52.07s-.36-.02-.52-.07l-4.55 4.56c.05.16.07.33.07.51 0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2c.18 0 .35.02.51.07l4.56-4.55C8.02 9.36 8 9.18 8 9c0-1.1.9-2 2-2s2 .9 2 2c0 .18-.02.36-.07.52l2.55 2.55c.16-.05.34-.07.52-.07s.36.02.52.07l3.55-3.56C19.02 8.35 19 8.18 19 8c0-1.1.9-2 2-2s2 .9 2 2z"/>',toc:'<path d="M3 9h14V7H3v2z"/><path d="M3 13h14v-2H3v2z"/><path d="M3 17h14v-2H3v2z"/><path d="M19 13h2v-2h-2v2zm0-6v2h2V7h-2zm0 10h2v-2h-2v2z"/>',today:'<path d="M19 19H5V8h14v11zm0-16h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M7 10h5v5H7z"/>',toll:'<path d="M3 12c0-2.61 1.67-4.83 4-5.65V4.26C3.55 5.15 1 8.27 1 12s2.55 6.85 6 7.74v-2.09c-2.33-.82-4-3.04-4-5.65z"/>',touch_app:'<path d="M9 11.24V7.5a2.5 2.5 0 0 1 5 0v3.74c1.21-.81 2-2.18 2-3.74C16 5.01 13.99 3 11.5 3S7 5.01 7 7.5c0 1.56.79 2.93 2 3.74z"/><path d="M18.84 15.87l-4.54-2.26c-.17-.07-.35-.11-.54-.11H13v-6c0-.83-.67-1.5-1.5-1.5S10 6.67 10 7.5v10.74l-3.43-.72c-.08-.01-.15-.03-.24-.03-.31 0-.59.13-.79.33l-.79.8 4.94 4.94c.27.27.65.44 1.06.44h6.79c.75 0 1.33-.55 1.44-1.28l.75-5.27c.01-.07.02-.14.02-.2 0-.62-.38-1.16-.91-1.38z"/>',track_changes:'<path d="M19.07 4.93l-1.41 1.41C19.1 7.79 20 9.79 20 12c0 4.42-3.58 8-8 8s-8-3.58-8-8c0-4.08 3.05-7.44 7-7.93v2.02C8.16 6.57 6 9.03 6 12c0 3.31 2.69 6 6 6s6-2.69 6-6c0-1.66-.67-3.16-1.76-4.24l-1.41 1.41C15.55 9.9 16 10.9 16 12c0 2.21-1.79 4-4 4s-4-1.79-4-4c0-1.86 1.28-3.41 3-3.86v2.14c-.6.35-1 .98-1 1.72 0 1.1.9 2 2 2s2-.9 2-2c0-.74-.4-1.38-1-1.72V2h-1C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10c0-2.76-1.12-5.26-2.93-7.07z"/>',translate:'<path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04z"/><path d="M15.88 17l1.62-4.33L19.12 17h-3.24zm2.62-7h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12z"/>',trending_down:'<path d="M16 18l2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z"/>',trending_flat:'<path d="M22 12l-4-4v3H3v2h15v3z"/>',trending_up:'<path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>',turned_in:'<path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z"/>',turned_in_not:'<path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2zm0 15l-5-2.18L7 18V5h10v13z"/>',update:'<path d="M21 10.12h-6.78l2.74-2.82c-2.73-2.7-7.15-2.8-9.88-.1a6.875 6.875 0 0 0 0 9.79 7.02 7.02 0 0 0 9.88 0C18.32 15.65 19 14.08 19 12.1h2c0 1.98-.88 4.55-2.64 6.29-3.51 3.48-9.21 3.48-12.72 0-3.5-3.47-3.53-9.11-.02-12.58a8.987 8.987 0 0 1 12.65 0L21 3v7.12z"/><path d="M12.5 8v4.25l3.5 2.08-.72 1.21L11 13V8h1.5z"/>',verified_user:'<path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>',view_agenda:'<path d="M20 13H3c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h17c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1z"/><path d="M20 3H3c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h17c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1z"/>',view_array:'<path d="M4 18h3V5H4v13z"/><path d="M18 5v13h3V5h-3z"/><path d="M8 18h9V5H8v13z"/>',view_carousel:'<path d="M7 19h10V4H7v15z"/><path d="M2 17h4V6H2v11z"/><path d="M18 6v11h4V6h-4z"/>',view_column:'<path d="M10 18h5V5h-5v13z"/><path d="M4 18h5V5H4v13z"/><path d="M16 5v13h5V5h-5z"/>',view_day:'<path d="M2 21h19v-3H2v3z"/><path d="M20 8H3c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h17c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1z"/><path d="M2 3v3h19V3H2z"/>',view_headline:'<path d="M4 15h17v-2H4v2z"/><path d="M4 19h17v-2H4v2z"/><path d="M4 11h17V9H4v2z"/><path d="M4 5v2h17V5H4z"/>',view_list:'<path d="M4 14h4v-4H4v4z"/><path d="M4 19h4v-4H4v4z"/><path d="M4 9h4V5H4v4z"/><path d="M9 14h12v-4H9v4z"/><path d="M9 19h12v-4H9v4z"/><path d="M9 5v4h12V5H9z"/>',view_module:'<path d="M4 11h5V5H4v6z"/><path d="M4 18h5v-6H4v6z"/><path d="M10 18h5v-6h-5v6z"/><path d="M16 18h5v-6h-5v6z"/><path d="M10 11h5V5h-5v6z"/><path d="M16 5v6h5V5h-5z"/>',view_quilt:'<path d="M10 18h5v-6h-5v6z"/><path d="M4 18h5V5H4v13z"/><path d="M16 18h5v-6h-5v6z"/><path d="M10 5v6h11V5H10z"/>',view_stream:'<path d="M4 18h17v-6H4v6z"/><path d="M4 5v6h17V5H4z"/>',view_week:'<path d="M6 5H3c-.55 0-1 .45-1 1v12c0 .55.45 1 1 1h3c.55 0 1-.45 1-1V6c0-.55-.45-1-1-1z"/><path d="M20 5h-3c-.55 0-1 .45-1 1v12c0 .55.45 1 1 1h3c.55 0 1-.45 1-1V6c0-.55-.45-1-1-1z"/><path d="M13 5h-3c-.55 0-1 .45-1 1v12c0 .55.45 1 1 1h3c.55 0 1-.45 1-1V6c0-.55-.45-1-1-1z"/>',visibility:'<path d="M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-12.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5z"/><path d="M12 9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>',visibility_off:'<path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7z"/><path d="M7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 0 0 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27z"/><path d="M11.84 9.02l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>',watch_later:'<path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z"/>',work:'<path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"/>',youtube_searched_for:'<path d="M17.01 14h-.8l-.27-.27c.98-1.14 1.57-2.61 1.57-4.23 0-3.59-2.91-6.5-6.5-6.5s-6.5 3-6.5 6.5H2l3.84 4 4.16-4H6.51C6.51 7 8.53 5 11.01 5s4.5 2.01 4.5 4.5c0 2.48-2.02 4.5-4.5 4.5-.65 0-1.26-.14-1.82-.38L7.71 15.1c.97.57 2.09.9 3.3.9 1.61 0 3.08-.59 4.22-1.57l.27.27v.79l5.01 4.99L22 19l-4.99-5z"/>',zoom_in:'<path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/><path d="M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z"/>',zoom_out:'<path d="M9.5 14C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zm6 0h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5z"/><path d="M7 9h5v1H7z"/>',add_alert:'<path d="M10.01 21.01c0 1.1.89 1.99 1.99 1.99s1.99-.89 1.99-1.99h-3.98zm8.87-4.19V11c0-3.25-2.25-5.97-5.29-6.69v-.72C13.59 2.71 12.88 2 12 2s-1.59.71-1.59 1.59v.72C7.37 5.03 5.12 7.75 5.12 11v5.82L3 18.94V20h18v-1.06l-2.12-2.12zM16 13.01h-3v3h-2v-3H8V11h3V8h2v3h3v2.01z"/>',error:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>',error_outline:'<path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>',warning:'<path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>',add_to_queue:'<path d="M21 17H3V5h18v12zm0-14H3c-1.11 0-2 .89-2 2v12a2 2 0 0 0 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5a2 2 0 0 0-2-2z"/><path d="M16 10v2h-3v3h-2v-3H8v-2h3V7h2v3h3z"/>',airplay:'<path d="M6 22h12l-6-6z"/><path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4v-2H3V5h18v12h-4v2h4c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',album:'<path d="M12 16.5c-2.49 0-4.5-2.01-4.5-4.5S9.51 7.5 12 7.5s4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M12 11c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/>',art_track:'<path d="M22 13h-8v-2h8v2z"/><path d="M22 7h-8v2h8V7z"/><path d="M14 17h8v-2h-8v2z"/><path d="M10.5 15l-2.25-3-1.75 2.26-1.25-1.51L3.5 15h7zM12 9v6c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V9c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2z"/>',av_timer:'<path d="M11 17c0 .55.45 1 1 1s1-.45 1-1-.45-1-1-1-1 .45-1 1z"/><path d="M11 3v4h2V5.08c3.39.49 6 3.39 6 6.92 0 3.87-3.13 7-7 7s-7-3.13-7-7c0-1.68.59-3.22 1.58-4.42L12 13l1.41-1.41-6.8-6.8v.02C4.42 6.45 3 9.05 3 12c0 4.97 4.02 9 9 9 4.97 0 9-4.03 9-9s-4.03-9-9-9h-1z"/><path d="M18 12c0-.55-.45-1-1-1s-1 .45-1 1 .45 1 1 1 1-.45 1-1z"/><path d="M6 12c0 .55.45 1 1 1s1-.45 1-1-.45-1-1-1-1 .45-1 1z"/>',branding_watermark:'<path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16h-9v-6h9v6z"/>',call_to_action:'<path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3v-3h18v3z"/>',closed_caption:'<path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z"/>',equalizer:'<path d="M10 20h4V4h-4v16z"/><path d="M4 20h4v-8H4v8z"/><path d="M16 9v11h4V9h-4z"/>',explicit:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4 6h-4v2h4v2h-4v2h4v2H9V7h6v2z"/>',fast_forward:'<path d="M4 18l8.5-6L4 6v12z"/><path d="M13 6v12l8.5-6L13 6z"/>',fast_rewind:'<path d="M11 18V6l-8.5 6 8.5 6z"/><path d="M11.5 12l8.5 6V6l-8.5 6z"/>',featured_play_list:'<path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 8H3V9h9v2zm0-4H3V5h9v2z"/>',featured_video:'<path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 9H3V5h9v7z"/>',fibre_dvr:'<path d="M17.5 10.5h2v1h-2zm-13 0h2v3h-2zM21 3H3c-1.11 0-2 .89-2 2v14c0 1.1.89 2 2 2h18c1.11 0 2-.9 2-2V5c0-1.11-.89-2-2-2zM8 13.5c0 .85-.65 1.5-1.5 1.5H3V9h3.5c.85 0 1.5.65 1.5 1.5v3zm4.62 1.5h-1.5L9.37 9h1.5l1 3.43 1-3.43h1.5l-1.75 6zM21 11.5c0 .6-.4 1.15-.9 1.4L21 15h-1.5l-.85-2H17.5v2H16V9h3.5c.85 0 1.5.65 1.5 1.5v1z"/>',fiber_manual_record:'<circle cx="12" cy="12" r="8"/>',fibre_new:'<path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zM8.5 15H7.3l-2.55-3.5V15H3.5V9h1.25l2.5 3.5V9H8.5v6zm5-4.74H11v1.12h2.5v1.26H11v1.11h2.5V15h-4V9h4v1.26zm7 3.74c0 .55-.45 1-1 1h-4c-.55 0-1-.45-1-1V9h1.25v4.51h1.13V9.99h1.25v3.51h1.12V9h1.25v5z"/>',fibre_pin:'<path d="M5.5 10.5h2v1h-2zM20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zM9 11.5c0 .85-.65 1.5-1.5 1.5h-2v2H4V9h3.5c.85 0 1.5.65 1.5 1.5v1zm3.5 3.5H11V9h1.5v6zm7.5 0h-1.2l-2.55-3.5V15H15V9h1.25l2.5 3.5V9H20v6z"/>',fibre_smart_record:'<path d="M17 4.26v2.09c2.33.82 4 3.04 4 5.65s-1.67 4.83-4 5.65v2.09c3.45-.89 6-4.01 6-7.74s-2.55-6.85-6-7.74z"/>',forward_10:'<path d="M4 13c0 4.4 3.6 8 8 8s8-3.6 8-8h-2c0 3.3-2.7 6-6 6s-6-2.7-6-6 2.7-6 6-6v4l5-5-5-5v4c-4.4 0-8 3.6-8 8z"/><path d="M10.8 16H10v-3.3L9 13v-.7l1.8-.6h.1V16z"/><path d="M14.3 13.4v-.5s-.1-.2-.1-.3c0-.1-.1-.1-.2-.2s-.2-.1-.3-.1c-.1 0-.2 0-.3.1l-.2.2s-.1.2-.1.3v2s.1.2.1.3c0 .1.1.1.2.2s.2.1.3.1c.1 0 .2 0 .3-.1l.2-.2s.1-.2.1-.3v-1.5zm.8.8c0 .3 0 .6-.1.8l-.3.6s-.3.3-.5.3-.4.1-.6.1c-.2 0-.4 0-.6-.1-.2-.1-.3-.2-.5-.3-.2-.1-.2-.3-.3-.6-.1-.3-.1-.5-.1-.8v-.7c0-.3 0-.6.1-.8l.3-.6s.3-.3.5-.3.4-.1.6-.1c.2 0 .4 0 .6.1.2.1.3.2.5.3.2.1.2.3.3.6.1.3.1.5.1.8v.7z"/>',forward_30:'<path d="M9.6 13.5h.4c.2 0 .4-.1.5-.2.1-.1.2-.2.2-.4v-.2s-.1-.1-.1-.2-.1-.1-.2-.1h-.5s-.1.1-.2.1-.1.1-.1.2v.2h-1c0-.2 0-.3.1-.5s.2-.3.3-.4c.1-.1.3-.2.4-.2.1 0 .4-.1.5-.1.2 0 .4 0 .6.1.2.1.3.1.5.2s.2.2.3.4c.1.2.1.3.1.5v.3s-.1.2-.1.3c0 .1-.1.2-.2.2s-.2.1-.3.2c.2.1.4.2.5.4.1.2.2.4.2.6 0 .2 0 .4-.1.5-.1.1-.2.3-.3.4-.1.1-.3.2-.5.2s-.4.1-.6.1c-.2 0-.4 0-.5-.1-.1-.1-.3-.1-.5-.2s-.2-.2-.3-.4c-.1-.2-.1-.4-.1-.6h.8v.2s.1.1.1.2.1.1.2.1h.5s.1-.1.2-.1.1-.1.1-.2v-.5s-.1-.1-.1-.2-.1-.1-.2-.1h-.6v-.7z"/><path d="M14.4 13.4v-.5s-.1-.2-.1-.3c0-.1-.1-.1-.2-.2s-.2-.1-.3-.1c-.1 0-.2 0-.3.1l-.2.2s-.1.2-.1.3v2s.1.2.1.3c0 .1.1.1.2.2s.2.1.3.1c.1 0 .2 0 .3-.1l.2-.2s.1-.2.1-.3v-1.5zm.9.8c0 .3 0 .6-.1.8l-.3.6s-.3.3-.5.3-.4.1-.6.1c-.2 0-.4 0-.6-.1-.2-.1-.3-.2-.5-.3-.2-.1-.2-.3-.3-.6-.1-.3-.1-.5-.1-.8v-.7c0-.3 0-.6.1-.8l.3-.6s.3-.3.5-.3.4-.1.6-.1c.2 0 .4 0 .6.1.2.1.3.2.5.3.2.1.2.3.3.6.1.3.1.5.1.8v.7z"/><path d="M4 13c0 4.4 3.6 8 8 8s8-3.6 8-8h-2c0 3.3-2.7 6-6 6s-6-2.7-6-6 2.7-6 6-6v4l5-5-5-5v4c-4.4 0-8 3.6-8 8z"/>',forward_5:'<path d="M4 13c0 4.4 3.6 8 8 8s8-3.6 8-8h-2c0 3.3-2.7 6-6 6s-6-2.7-6-6 2.7-6 6-6v4l5-5-5-5v4c-4.4 0-8 3.6-8 8z"/><path d="M10.7 13.9l.2-2.2h2.4v.7h-1.7l-.1.9s.1 0 .1-.1.1 0 .1-.1.1 0 .2 0h.2c.2 0 .4 0 .5.1.1.1.3.2.4.3.1.1.2.3.3.5.1.2.1.4.1.6 0 .2 0 .4-.1.5-.1.1-.1.3-.3.5-.2.2-.3.2-.5.3-.2.1-.4.1-.6.1-.2 0-.4 0-.5-.1-.1-.1-.3-.1-.5-.2s-.2-.2-.3-.4c-.1-.2-.1-.3-.1-.5h.8c0 .2.1.3.2.4.1.1.2.1.4.1.1 0 .2 0 .3-.1l.2-.2s.1-.2.1-.3v-.6l-.1-.2-.2-.2s-.2-.1-.3-.1h-.2s-.1 0-.2.1-.1 0-.1.1-.1.1-.1.1h-.6z"/>',games:'<path d="M15 7.5V2H9v5.5l3 3 3-3z"/><path d="M7.5 9H2v6h5.5l3-3-3-3z"/><path d="M9 16.5V22h6v-5.5l-3-3-3 3z"/><path d="M16.5 9l-3 3 3 3H22V9h-5.5z"/>',hd:'<path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-8 12H9.5v-2h-2v2H6V9h1.5v2.5h2V9H11v6zm2-6h4c.55 0 1 .45 1 1v4c0 .55-.45 1-1 1h-4V9zm1.5 4.5h2v-3h-2v3z"/>',hearing:'<path d="M17 20c-.29 0-.56-.06-.76-.15-.71-.37-1.21-.88-1.71-2.38-.51-1.56-1.47-2.29-2.39-3-.79-.61-1.61-1.24-2.32-2.53C9.29 10.98 9 9.93 9 9c0-2.8 2.2-5 5-5s5 2.2 5 5h2c0-3.93-3.07-7-7-7S7 5.07 7 9c0 1.26.38 2.65 1.07 3.9.91 1.65 1.98 2.48 2.85 3.15.81.62 1.39 1.07 1.71 2.05.6 1.82 1.37 2.84 2.73 3.55.51.23 1.07.35 1.64.35 2.21 0 4-1.79 4-4h-2c0 1.1-.9 2-2 2z"/><path d="M7.64 2.64L6.22 1.22C4.23 3.21 3 5.96 3 9s1.23 5.79 3.22 7.78l1.41-1.41C6.01 13.74 5 11.49 5 9s1.01-4.74 2.64-6.36z"/><path d="M11.5 9c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5-1.12-2.5-2.5-2.5-2.5 1.12-2.5 2.5z"/>',high_quality:'<path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 11H9.5v-2h-2v2H6V9h1.5v2.5h2V9H11v6zm7-1c0 .55-.45 1-1 1h-.75v1.5h-1.5V15H14c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v4zm-3.5-.5h2v-3h-2v3z"/>',my_library_add:'<path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6z"/><path d="M19 11h-4v4h-2v-4H9V9h4V5h2v4h4v2zm1-9H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>',my_library_books:'<path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6z"/><path d="M19 7H9V5h10v2zm-4 8H9v-2h6v2zm4-4H9V9h10v2zm1-9H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>',my_library_music:'<path d="M18 7h-3v5.5a2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1 2.5-2.5c.57 0 1.08.19 1.5.51V5h4v2zm2-5H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/><path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6z"/>',loop:'<path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8z"/><path d="M12 18c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>',mic:'<path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><path d="M17.3 11c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>',mic_none:'<path d="M10.8 4.9c0-.66.54-1.2 1.2-1.2.66 0 1.2.54 1.2 1.2l-.01 6.2c0 .66-.53 1.2-1.19 1.2-.66 0-1.2-.54-1.2-1.2V4.9zM12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><path d="M17.3 11c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>',mic_off:'<path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28z"/><path d="M14.98 11.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99z"/><path d="M4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>',movie:'<path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>',music_video:'<path d="M21 19H3V5h18v14zm0-16H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M8 15c0-1.66 1.34-3 3-3 .35 0 .69.07 1 .18V6h5v2h-3v7.03A3.003 3.003 0 0 1 11 18c-1.66 0-3-1.34-3-3z"/>',new_releases:'<path d="M23 12l-2.44-2.78.34-3.68-3.61-.82-1.89-3.18L12 3 8.6 1.54 6.71 4.72l-3.61.81.34 3.68L1 12l2.44 2.78-.34 3.69 3.61.82 1.89 3.18L12 21l3.4 1.46 1.89-3.18 3.61-.82-.34-3.68L23 12zm-10 5h-2v-2h2v2zm0-4h-2V7h2v6z"/>',not_interested:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z"/>',note:'<path d="M22 10l-6-6H4c-1.1 0-2 .9-2 2v12.01c0 1.1.9 1.99 2 1.99l16-.01c1.1 0 2-.89 2-1.99v-8zm-7-4.5l5.5 5.5H15V5.5z"/>',pause:'<path d="M6 19h4V5H6v14z"/><path d="M14 5v14h4V5h-4z"/>',pause_circle_filled:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>',pause_circle_outline:'<path d="M9 16h2V8H9v8z"/><path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M13 16h2V8h-2v8z"/>',play_arrow:'<path d="M8 5v14l11-7z"/>',play_circle_fill:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>',play_circle_outline:'<path d="M10 16.5l6-4.5-6-4.5v9z"/><path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>',playlist_add:'<path d="M14 10H2v2h12v-2z"/><path d="M14 6H2v2h12V6z"/><path d="M18 14v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z"/><path d="M2 16h8v-2H2v2z"/>',playlist_add_check:'<path d="M14 10H2v2h12v-2z"/><path d="M14 6H2v2h12V6z"/><path d="M2 16h8v-2H2v2z"/><path d="M21.5 11.5L23 13l-6.99 7-4.51-4.5L13 14l3.01 3 5.49-5.5z"/>',playlist_play:'<path d="M19 9H2v2h17V9z"/><path d="M19 5H2v2h17V5z"/><path d="M2 15h13v-2H2v2z"/><path d="M17 13v6l5-3-5-3z"/>',queue:'<path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6z"/><path d="M19 11h-4v4h-2v-4H9V9h4V5h2v4h4v2zm1-9H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>',queue_music:'<path d="M15 6H3v2h12V6z"/><path d="M15 10H3v2h12v-2z"/><path d="M3 16h8v-2H3v2z"/><path d="M17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>',queue_play_next:'<path d="M21 3H3c-1.11 0-2 .89-2 2v12a2 2 0 0 0 2 2h5v2h8v-2h2v-2H3V5h18v8h2V5a2 2 0 0 0-2-2z"/><path d="M13 10V7h-2v3H8v2h3v3h2v-3h3v-2h-3z"/><path d="M24 18l-4.5 4.5L18 21l3-3-3-3 1.5-1.5L24 18z"/>',radio:'<path d="M3.24 6.15C2.51 6.43 2 7.17 2 8v12c0 1.1.89 2 2 2h16c1.11 0 2-.9 2-2V8c0-1.11-.89-2-2-2H8.3l8.26-3.34L15.88 1 3.24 6.15zM7 20c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm13-8h-2v-2h-2v2H4V8h16v4z"/>',recent_actors:'<path d="M17 19h2V5h-2v14zm4-14v14h2V5h-2z"/><path d="M12.5 17h-9v-.75c0-1.5 3-2.25 4.5-2.25s4.5.75 4.5 2.25V17zM8 7.75c1.24 0 2.25 1.01 2.25 2.25S9.24 12.25 8 12.25 5.75 11.24 5.75 10 6.76 7.75 8 7.75zM14 5H2c-.55 0-1 .45-1 1v12c0 .55.45 1 1 1h12c.55 0 1-.45 1-1V6c0-.55-.45-1-1-1z"/>',remove_from_queue:'<path d="M21 17H3V5h18v12zm0-14H3c-1.11 0-2 .89-2 2v12a2 2 0 0 0 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5a2 2 0 0 0-2-2z"/><path d="M16 10v2H8v-2h8z"/>',repeat:'<path d="M7 7h10v3l4-4-4-4v3H5v6h2V7z"/><path d="M17 17H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>',repeat_one:'<path d="M7 7h10v3l4-4-4-4v3H5v6h2V7z"/><path d="M17 17H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/><path d="M13 15V9h-1l-2 1v1h1.5v4H13z"/>',replay:'<path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>',replay_10:'<path d="M12 5V1L7 6l5 5V7c3.3 0 6 2.7 6 6s-2.7 6-6 6-6-2.7-6-6H4c0 4.4 3.6 8 8 8s8-3.6 8-8-3.6-8-8-8z"/><path d="M10.9 16H10v-3.3L9 13v-.7l1.8-.6h.1V16z"/><path d="M14.3 13.4v-.5s-.1-.2-.1-.3c0-.1-.1-.1-.2-.2s-.2-.1-.3-.1c-.1 0-.2 0-.3.1l-.2.2s-.1.2-.1.3v2s.1.2.1.3c0 .1.1.1.2.2s.2.1.3.1c.1 0 .2 0 .3-.1l.2-.2s.1-.2.1-.3v-1.5zm.9.8c0 .3 0 .6-.1.8l-.3.6s-.3.3-.5.3-.4.1-.6.1c-.2 0-.4 0-.6-.1-.2-.1-.3-.2-.5-.3-.2-.1-.2-.3-.3-.6-.1-.3-.1-.5-.1-.8v-.7c0-.3 0-.6.1-.8l.3-.6s.3-.3.5-.3.4-.1.6-.1c.2 0 .4 0 .6.1.2.1.3.2.5.3.2.1.2.3.3.6.1.3.1.5.1.8v.7z"/>',replay_30:'<path d="M12 5V1L7 6l5 5V7c3.3 0 6 2.7 6 6s-2.7 6-6 6-6-2.7-6-6H4c0 4.4 3.6 8 8 8s8-3.6 8-8-3.6-8-8-8z"/><path d="M9.6 13.5h.4c.2 0 .4-.1.5-.2.1-.1.2-.2.2-.4v-.2s-.1-.1-.1-.2-.1-.1-.2-.1h-.5s-.1.1-.2.1-.1.1-.1.2v.2h-1c0-.2 0-.3.1-.5s.2-.3.3-.4c.1-.1.3-.2.4-.2.1 0 .4-.1.5-.1.2 0 .4 0 .6.1.2.1.3.1.5.2s.2.2.3.4c.1.2.1.3.1.5v.3s-.1.2-.1.3c0 .1-.1.2-.2.2s-.2.1-.3.2c.2.1.4.2.5.4.1.2.2.4.2.6 0 .2 0 .4-.1.5-.1.1-.2.3-.3.4-.1.1-.3.2-.5.2s-.4.1-.6.1c-.2 0-.4 0-.5-.1-.1-.1-.3-.1-.5-.2s-.2-.2-.3-.4c-.1-.2-.1-.4-.1-.6h.8v.2s.1.1.1.2.1.1.2.1h.5s.1-.1.2-.1.1-.1.1-.2v-.5s-.1-.1-.1-.2-.1-.1-.2-.1h-.6v-.7z"/><path d="M14.5 13.4v-.5c0-.1-.1-.2-.1-.3 0-.1-.1-.1-.2-.2s-.2-.1-.3-.1c-.1 0-.2 0-.3.1l-.2.2s-.1.2-.1.3v2s.1.2.1.3c0 .1.1.1.2.2s.2.1.3.1c.1 0 .2 0 .3-.1l.2-.2s.1-.2.1-.3v-1.5zm.8.8c0 .3 0 .6-.1.8l-.3.6s-.3.3-.5.3-.4.1-.6.1c-.2 0-.4 0-.6-.1-.2-.1-.3-.2-.5-.3-.2-.1-.2-.3-.3-.6-.1-.3-.1-.5-.1-.8v-.7c0-.3 0-.6.1-.8l.3-.6s.3-.3.5-.3.4-.1.6-.1c.2 0 .4 0 .6.1.2.1.3.2.5.3.2.1.2.3.3.6.1.3.1.5.1.8v.7z"/>',replay_5:'<path d="M12 5V1L7 6l5 5V7c3.3 0 6 2.7 6 6s-2.7 6-6 6-6-2.7-6-6H4c0 4.4 3.6 8 8 8s8-3.6 8-8-3.6-8-8-8z"/><path d="M10.7 13.9l.2-2.2h2.4v.7h-1.7l-.1.9s.1 0 .1-.1.1 0 .1-.1.1 0 .2 0h.2c.2 0 .4 0 .5.1.1.1.3.2.4.3.1.1.2.3.3.5.1.2.1.4.1.6 0 .2 0 .4-.1.5-.1.1-.1.3-.3.5-.2.2-.3.2-.4.3-.1.1-.4.1-.6.1-.2 0-.4 0-.5-.1-.1-.1-.3-.1-.5-.2s-.2-.2-.3-.4c-.1-.2-.1-.3-.1-.5h.8c0 .2.1.3.2.4.1.1.2.1.4.1.1 0 .2 0 .3-.1l.2-.2s.1-.2.1-.3v-.6l-.1-.2-.2-.2s-.2-.1-.3-.1h-.2s-.1 0-.2.1-.1 0-.1.1-.1.1-.1.1h-.7z"/>',shuffle:'<path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41z"/><path d="M14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5z"/><path d="M14.83 13.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z"/>',skip_next:'<path d="M6 18l8.5-6L6 6v12z"/><path d="M16 6v12h2V6h-2z"/>',skip_previous:'<path d="M6 6h2v12H6z"/><path d="M9.5 12l8.5 6V6z"/>',slow_motion_video:'<path d="M13.05 9.79L10 7.5v9l3.05-2.29L16 12z"/><path d="M13.05 9.79L10 7.5v9l3.05-2.29L16 12z"/><path d="M13.05 9.79L10 7.5v9l3.05-2.29L16 12z"/><path d="M11 4.07V2.05c-2.01.2-3.84 1-5.32 2.21L7.1 5.69A7.941 7.941 0 0 1 11 4.07z"/><path d="M5.69 7.1L4.26 5.68A9.949 9.949 0 0 0 2.05 11h2.02c.18-1.46.76-2.79 1.62-3.9z"/><path d="M4.07 13H2.05c.2 2.01 1 3.84 2.21 5.32l1.43-1.43A7.868 7.868 0 0 1 4.07 13z"/><path d="M5.68 19.74A9.981 9.981 0 0 0 11 21.95v-2.02a7.941 7.941 0 0 1-3.9-1.62l-1.42 1.43z"/><path d="M22 12c0 5.16-3.92 9.42-8.95 9.95v-2.02C16.97 19.41 20 16.05 20 12s-3.03-7.41-6.95-7.93V2.05C18.08 2.58 22 6.84 22 12z"/>',snooze:'<path d="M7.88 3.39L6.6 1.86 2 5.71l1.29 1.53 4.59-3.85z"/><path d="M22 5.72l-4.6-3.86-1.29 1.53 4.6 3.86L22 5.72z"/><path d="M12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm0-16c-4.97 0-9 4.03-9 9s4.02 9 9 9c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/><path d="M9 11h3.63L9 15.2V17h6v-2h-3.63L15 10.8V9H9v2z"/>',sort_by_alpha:'<path d="M14.94 4.66h-4.72l2.36-2.36z"/><path d="M10.25 19.37h4.66l-2.33 2.33z"/><path d="M4.97 13.64l1.94-5.18 1.94 5.18H4.97zM6.1 6.27L1.6 17.73h1.84l.92-2.45h5.11l.92 2.45h1.84L7.74 6.27H6.1z"/><path d="M15.73 16.14h6.12v1.59h-8.53v-1.29l5.92-8.56h-5.88v-1.6h8.3v1.26l-5.93 8.6z"/>',stop:'<path d="M6 6h12v12H6z"/>',subscriptions:'<path d="M20 8H4V6h16v2z"/><path d="M18 2H6v2h12V2z"/><path d="M16 16l-6-3.27v6.53L16 16zm6-4v8c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-8c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2z"/>',subtitles:'<path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 12h4v2H4v-2zm10 6H4v-2h10v2zm6 0h-4v-2h4v2zm0-4H10v-2h10v2z"/>',surround_sound:'<path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM7.76 16.24l-1.41 1.41C4.78 16.1 4 14.05 4 12c0-2.05.78-4.1 2.34-5.66l1.41 1.41C6.59 8.93 6 10.46 6 12s.59 3.07 1.76 4.24zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm5.66 1.66l-1.41-1.41C17.41 15.07 18 13.54 18 12s-.59-3.07-1.76-4.24l1.41-1.41C19.22 7.9 20 9.95 20 12c0 2.05-.78 4.1-2.34 5.66zM12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>',video_call:' <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4zM14 13h-3v3H9v-3H6v-2h3V8h2v3h3v2z"/>',video_label:'<path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 13H3V5h18v11z"/>',video_library:'<path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6z"/><path d="M12 14.5v-9l6 4.5-6 4.5zM20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>',videocam:'<path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>',videocam_off:'<path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5z"/><path d="M3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/>',volume_down:'<path d="M18.5 12A4.5 4.5 0 0 0 16 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/><path d="M5 9v6h4l5 5V4L9 9H5z"/>',volume_mute:'<path d="M7 9v6h4l5 5V4l-5 5H7z"/>',volume_off:'<path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63z"/><path d="M19 12c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71z"/><path d="M4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3z"/><path d="M12 4L9.91 6.09 12 8.18V4z"/>',volume_up:'<path d="M3 9v6h4l5 5V4L7 9H3z"/><path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/><path d="M14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77 0-4.28-2.99-7.86-7-8.77z"/>',web:'<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 14H4v-4h11v4zm0-5H4V9h11v4zm5 5h-4V9h4v9z"/>',web_asset:'<path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.89-2-2-2zm0 14H5V8h14v10z" />',business:'<path d="M20 19h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zM10 7H8V5h2v2zm0 4H8V9h2v2zm0 4H8v-2h2v2zm0 4H8v-2h2v2zM6 7H4V5h2v2zm0 4H4V9h2v2zm0 4H4v-2h2v2zm0 4H4v-2h2v2zm6-12V3H2v18h20V7H12z"/><path d="M18 11h-2v2h2v-2z"/><path d="M18 15h-2v2h2v-2z"/>',call:'<path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>',call_end:'<path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>',call_made:'<path d="M9 5v2h6.59L4 18.59 5.41 20 17 8.41V15h2V5z"/>',call_merge:'<path d="M17 20.41L18.41 19 15 15.59 13.59 17 17 20.41z"/><path d="M7.5 8H11v5.59L5.59 19 7 20.41l6-6V8h3.5L12 3.5 7.5 8z"/>',call_missed:'<path d="M19.59 7L12 14.59 6.41 9H11V7H3v8h2v-4.59l7 7 9-9z"/>',call_missed_outgoing:'<path d="M3 8.41l9 9 7-7V15h2V7h-8v2h4.59L12 14.59 4.41 7 3 8.41z"/>',call_received:'<path d="M20 5.41L18.59 4 7 15.59V9H5v10h10v-2H8.41z"/>',call_split:'<path d="M14 4l2.29 2.29-2.88 2.88 1.42 1.42 2.88-2.88L20 10V4z"/><path d="M10 4H4v6l2.29-2.29 4.71 4.7V20h2v-8.41l-5.29-5.3z"/>',chat:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"/>',chat_bubble:'<path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>',chat_bubble_outline:'<path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>',clear_all:'<path d="M5 13h14v-2H5v2z"/><path d="M3 17h14v-2H3v2z"/><path d="M7 7v2h14V7H7z"/>',comment:'<path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18zM18 14H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>',contact_mail:'<path d="M21 8V7l-3 2-3-2v1l3 2 3-2zm1-5H2C.9 3 0 3.9 0 5v14c0 1.1.9 2 2 2h20c1.1 0 1.99-.9 1.99-2L24 5c0-1.1-.9-2-2-2zM8 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm6 12H2v-1c0-2 4-3.1 6-3.1s6 1.1 6 3.1v1zm8-6h-8V6h8v6z"/>',contact_phone:'<path d="M22 3H2C.9 3 0 3.9 0 5v14c0 1.1.9 2 2 2h20c1.1 0 1.99-.9 1.99-2L24 5c0-1.1-.9-2-2-2zM8 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm6 12H2v-1c0-2 4-3.1 6-3.1s6 1.1 6 3.1v1zm3.85-4h1.64L21 16l-1.99 1.99c-1.31-.98-2.28-2.38-2.73-3.99-.18-.64-.28-1.31-.28-2s.1-1.36.28-2c.45-1.62 1.42-3.01 2.73-3.99L21 8l-1.51 2h-1.64c-.22.63-.35 1.3-.35 2s.13 1.37.35 2z"/>',contacts:'<path d="M20 0H4v2h16V0z"/><path d="M4 24h16v-2H4v2z"/><path d="M17 17H7v-1.5c0-1.67 3.33-2.5 5-2.5s5 .83 5 2.5V17zM12 6.75c1.24 0 2.25 1.01 2.25 2.25s-1.01 2.25-2.25 2.25S9.75 10.24 9.75 9 10.76 6.75 12 6.75zM20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z"/>',dialer_sip:'<path d="M20 5h-1V4h1v1zm-2-2v5h1V6h2V3h-3zm-3 2h-2V4h2V3h-3v3h2v1h-2v1h3V5zm2-2h-1v5h1V3z"/><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.01.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.59l2.2-2.21c.27-.26.35-.65.24-1C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"/>',dialpad:'<path d="M12 19c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M6 13c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M18 7c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/><path d="M12 1c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>',email:'<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>',forum:'<path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1z"/><path d="M17 12V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z"/>',import_contacts:'<path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z"/>',import_export:'<path d="M9 3L5 6.99h3V14h2V6.99h3L9 3z"/><path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3z"/>',
invert_colors_off:'<path d="M12 19.59c-1.6 0-3.11-.62-4.24-1.76A5.945 5.945 0 0 1 6 13.59c0-1.32.43-2.57 1.21-3.6L12 14.77v4.82zm8.65 1.28l-2.35-2.35-6.3-6.29-3.56-3.57-1.42-1.41L4.27 4.5 3 5.77l2.78 2.78a8.005 8.005 0 0 0 .56 10.69A7.98 7.98 0 0 0 12 21.58c1.79 0 3.57-.59 5.03-1.78l2.7 2.7L21 21.23l-.35-.36z"/><path d="M12 5.1v4.58l7.25 7.26c1.37-2.96.84-6.57-1.6-9.01L12 2.27l-3.7 3.7 1.41 1.41L12 5.1z"/>',live_help:'<path d="M19 2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h4l3 3 3-3h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-6 16h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 11.9 13 12.5 13 14h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>',location_off:'<path d="M12 6.5A2.5 2.5 0 0 1 14.5 9c0 .74-.33 1.39-.83 1.85l3.63 3.63c.98-1.86 1.7-3.8 1.7-5.48 0-3.87-3.13-7-7-7a7 7 0 0 0-5.04 2.15l3.19 3.19c.46-.52 1.11-.84 1.85-.84z"/><path d="M16.37 16.1l-4.63-4.63-.11-.11L3.27 3 2 4.27l3.18 3.18C5.07 7.95 5 8.47 5 9c0 5.25 7 13 7 13s1.67-1.85 3.38-4.35L18.73 21 20 19.73l-3.63-3.63z"/>',location_on:'<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>',mail_outline:'<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"/>',message:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>',no_sim:'<path d="M18.99 5c0-1.1-.89-2-1.99-2h-7L7.66 5.34 19 16.68 18.99 5z"/><path d="M3.65 3.88L2.38 5.15 5 7.77V19c0 1.1.9 2 2 2h10.01c.35 0 .67-.1.96-.26l1.88 1.88 1.27-1.27L3.65 3.88z"/>',phone:'<path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>',phonelink_erase:'<path d="M13 8.2l-1-1-4 4-4-4-1 1 4 4-4 4 1 1 4-4 4 4 1-1-4-4 4-4zM19 1H9c-1.1 0-2 .9-2 2v3h2V4h10v16H9v-2H7v3c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/>',phonelink_lock:'<path d="M19 1H9c-1.1 0-2 .9-2 2v3h2V4h10v16H9v-2H7v3c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm-8.2 10V9.5C10.8 8.1 9.4 7 8 7S5.2 8.1 5.2 9.5V11c-.6 0-1.2.6-1.2 1.2v3.5c0 .7.6 1.3 1.2 1.3h5.5c.7 0 1.3-.6 1.3-1.2v-3.5c0-.7-.6-1.3-1.2-1.3zm-1.3 0h-3V9.5c0-.8.7-1.3 1.5-1.3s1.5.5 1.5 1.3V11z"/>',phonelink_ring:'<path d="M20.1 7.7l-1 1c1.8 1.8 1.8 4.6 0 6.5l1 1c2.5-2.3 2.5-6.1 0-8.5zM18 9.8l-1 1c.5.7.5 1.6 0 2.3l1 1c1.2-1.2 1.2-3 0-4.3zM14 1H4c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm0 19H4V4h10v16z"/>',phonelink_setup:'<path d="M11.8 12.5v-1l1.1-.8c.1-.1.1-.2.1-.3l-1-1.7c-.1-.1-.2-.2-.3-.1l-1.3.4c-.3-.2-.6-.4-.9-.5l-.2-1.3c0-.1-.1-.2-.3-.2H7c-.1 0-.2.1-.3.2l-.2 1.3c-.3.1-.6.3-.9.5l-1.3-.5c-.1 0-.2 0-.3.1l-1 1.7c-.1.1 0 .2.1.3l1.1.8v1l-1.1.8c-.1.2-.1.3-.1.4l1 1.7c.1.1.2.2.3.1l1.4-.4c.3.2.6.4.9.5l.2 1.3c-.1.1.1.2.2.2h2c.1 0 .2-.1.3-.2l.2-1.3c.3-.1.6-.3.9-.5l1.3.5c.1 0 .2 0 .3-.1l1-1.7c.1-.1 0-.2-.1-.3l-1.1-.9zM8 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM19 1H9c-1.1 0-2 .9-2 2v3h2V4h10v16H9v-2H7v3c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/>',portable_wifi_off:'<path d="M17.56 14.24c.28-.69.44-1.45.44-2.24 0-3.31-2.69-6-6-6-.79 0-1.55.16-2.24.44l1.62 1.62c.2-.03.41-.06.62-.06a3.999 3.999 0 0 1 3.95 4.63l1.61 1.61z"/><path d="M12 4c4.42 0 8 3.58 8 8 0 1.35-.35 2.62-.95 3.74l1.47 1.47A9.86 9.86 0 0 0 22 12c0-5.52-4.48-10-10-10-1.91 0-3.69.55-5.21 1.47l1.46 1.46C9.37 4.34 10.65 4 12 4z"/><path d="M3.27 2.5L2 3.77l2.1 2.1C2.79 7.57 2 9.69 2 12c0 3.7 2.01 6.92 4.99 8.65l1-1.73C5.61 17.53 4 14.96 4 12c0-1.76.57-3.38 1.53-4.69l1.43 1.44C6.36 9.68 6 10.8 6 12c0 2.22 1.21 4.15 3 5.19l1-1.74c-1.19-.7-2-1.97-2-3.45 0-.65.17-1.25.44-1.79l1.58 1.58L10 12c0 1.1.9 2 2 2l.21-.02.01.01 7.51 7.51L21 20.23 4.27 3.5l-1-1z"/>',present_to_all:'<path d="M21 19.02H3V4.98h18v14.04zM21 3H3c-1.11 0-2 .89-2 2v14c0 1.11.89 2 2 2h18c1.11 0 2-.89 2-2V5c0-1.11-.89-2-2-2z"/><path d="M10 12H8l4-4 4 4h-2v4h-4v-4z"/>',ring_volume:'<path d="M23.71 16.67C20.66 13.78 16.54 12 12 12 7.46 12 3.34 13.78.29 16.67c-.18.18-.29.43-.29.71 0 .28.11.53.29.71l2.48 2.48c.18.18.43.29.71.29.27 0 .52-.11.7-.28.79-.74 1.69-1.36 2.66-1.85.33-.16.56-.5.56-.9v-3.1c1.45-.48 3-.73 4.6-.73 1.6 0 3.15.25 4.6.72v3.1c0 .39.23.74.56.9.98.49 1.87 1.12 2.66 1.85.18.18.43.28.7.28.28 0 .53-.11.71-.29l2.48-2.48c.18-.18.29-.43.29-.71 0-.27-.11-.52-.29-.7z"/><path d="M21.16 6.26l-1.41-1.41-3.56 3.55 1.41 1.41s3.45-3.52 3.56-3.55z"/><path d="M13 2h-2v5h2V2z"/><path d="M6.4 9.81L7.81 8.4 4.26 4.84 2.84 6.26c.11.03 3.56 3.55 3.56 3.55z"/>',rss_feed:'<path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56z"/><path d="M4 10.1v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z"/>',screen_share:'<path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.11-.9-2-2-2H4c-1.11 0-2 .89-2 2v10c0 1.1.89 2 2 2H0v2h24v-2h-4zm-7-3.53v-2.19c-2.78 0-4.61.85-6 2.72.56-2.67 2.11-5.33 6-5.87V7l4 3.73-4 3.74z"/>',stay_current_landscape:'<path d="M1.01 7L1 17c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2H3c-1.1 0-1.99.9-1.99 2zM19 7v10H5V7h14z"/>',stay_current_portrait:'<path d="M17 1.01L7 1c-1.1 0-1.99.9-1.99 2v18c0 1.1.89 2 1.99 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/>',stay_primary_landscape:'<path d="M1.01 7L1 17c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2H3c-1.1 0-1.99.9-1.99 2zM19 7v10H5V7h14z"/>',stay_primary_portrait:'<path d="M17 1.01L7 1c-1.1 0-1.99.9-1.99 2v18c0 1.1.89 2 1.99 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/>',stop_screen_share:'<path d="M21.22 18.02l2 2H24v-2h-2.78z"/><path d="M21.99 16.02l.01-10a2 2 0 0 0-2-2H7.22l5.23 5.23c.18-.04.36-.07.55-.1V7.02l4 3.73-1.58 1.47 5.54 5.54c.61-.33 1.03-.99 1.03-1.74z"/><path d="M7 15.02c.31-1.48.92-2.95 2.07-4.06l1.59 1.59c-1.54.38-2.7 1.18-3.66 2.47zM2.39 1.73L1.11 3l1.54 1.54c-.4.36-.65.89-.65 1.48v10a2 2 0 0 0 2 2H0v2h18.13l2.71 2.71 1.27-1.27L2.39 1.73z"/>',swap_calls:'<path d="M18 4l-4 4h3v7c0 1.1-.9 2-2 2s-2-.9-2-2V8c0-2.21-1.79-4-4-4S5 5.79 5 8v7H2l4 4 4-4H7V8c0-1.1.9-2 2-2s2 .9 2 2v7c0 2.21 1.79 4 4 4s4-1.79 4-4V8h3l-4-4z"/>',textsms:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 11H7V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z"/>',voicemail:'<path d="M18.5 6C15.46 6 13 8.46 13 11.5c0 1.33.47 2.55 1.26 3.5H9.74c.79-.95 1.26-2.17 1.26-3.5C11 8.46 8.54 6 5.5 6S0 8.46 0 11.5 2.46 17 5.5 17h13c3.04 0 5.5-2.46 5.5-5.5S21.54 6 18.5 6zm-13 9C3.57 15 2 13.43 2 11.5S3.57 8 5.5 8 9 9.57 9 11.5 7.43 15 5.5 15zm13 0c-1.93 0-3.5-1.57-3.5-3.5S16.57 8 18.5 8 22 9.57 22 11.5 20.43 15 18.5 15z"/>',vpn_key:'<path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>',add:'<path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>',add_box:'<path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>',add_circle:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>',add_circle_outline:'<path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7z"/><path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>',archive:'<path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z"/>',backspace:'<path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"/>',block:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>',clear:'<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>',content_copy:'<path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1z"/><path d="M19 21H8V7h11v14zm0-16H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2z"/>',content_cut:'<path d="M12 12.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5.5.22.5.5-.22.5-.5.5zM6 20a2 2 0 1 1-.001-3.999A2 2 0 0 1 6 20zM6 8a2 2 0 1 1-.001-3.999A2 2 0 0 1 6 8zm3.64-.36c.23-.5.36-1.05.36-1.64 0-2.21-1.79-4-4-4S2 3.79 2 6s1.79 4 4 4c.59 0 1.14-.13 1.64-.36L10 12l-2.36 2.36C7.14 14.13 6.59 14 6 14c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4c0-.59-.13-1.14-.36-1.64L12 14l7 7h3v-1L9.64 7.64z"/><path d="M19 3l-6 6 2 2 7-7V3z"/>',content_paste:'<path d="M19 2h-4.18C14.4.84 13.3 0 12 0c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm7 18H5V4h2v3h10V4h2v16z"/>',create:'<path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"/><path d="M20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>',delete_sweep:'<path d="M15 16h4v2h-4z"/><path d="M15 8h7v2h-7z"/><path d="M15 12h6v2h-6z"/><path d="M3 18c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2V8H3v10z"/><path d="M14 5h-3l-1-1H6L5 5H2v2h12z"/>',drafts:'<path d="M21.99 8c0-.72-.37-1.35-.94-1.7L12 1 2.95 6.3C2.38 6.65 2 7.28 2 8v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2l-.01-10zM12 13L3.74 7.84 12 3l8.26 4.84L12 13z"/>',filter_list:'<path d="M10 18h4v-2h-4v2z"/><path d="M3 6v2h18V6H3z"/><path d="M6 13h12v-2H6v2z"/>',flag:'<path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>',font_download:'<path d="M9.93 13.5h4.14L12 7.98z"/><path d="M15.95 18.5l-1.14-3H9.17l-1.12 3H5.96l5.11-13h1.86l5.11 13h-2.09zM20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>',forward:'<path d="M12 8V4l8 8-8 8v-4H4V8z"/>',gesture:'<path d="M4.59 6.89c.7-.71 1.4-1.35 1.71-1.22.5.2 0 1.03-.3 1.52-.25.42-2.86 3.89-2.86 6.31 0 1.28.48 2.34 1.34 2.98.75.56 1.74.73 2.64.46 1.07-.31 1.95-1.4 3.06-2.77 1.21-1.49 2.83-3.44 4.08-3.44 1.63 0 1.65 1.01 1.76 1.79-3.78.64-5.38 3.67-5.38 5.37 0 1.7 1.44 3.09 3.21 3.09 1.63 0 4.29-1.33 4.69-6.1H21v-2.5h-2.47c-.15-1.65-1.09-4.2-4.03-4.2-2.25 0-4.18 1.91-4.94 2.84-.58.73-2.06 2.48-2.29 2.72-.25.3-.68.84-1.11.84-.45 0-.72-.83-.36-1.92.35-1.09 1.4-2.86 1.85-3.52.78-1.14 1.3-1.92 1.3-3.28C8.95 3.69 7.31 3 6.44 3 5.12 3 3.97 4 3.72 4.25c-.36.36-.66.66-.88.93l1.75 1.71zm9.29 11.66c-.31 0-.74-.26-.74-.72 0-.6.73-2.2 2.87-2.76-.3 2.69-1.43 3.48-2.13 3.48z"/>',inbox:'<path d="M19 15h-4c0 1.66-1.34 3-3 3s-3-1.34-3-3H4.99V5H19v10zm0-12H4.99c-1.1 0-1.98.9-1.98 2L3 19c0 1.1.89 2 1.99 2H19c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M16 10h-2V7h-4v3H8l4 4 4-4z"/>',link:'<path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1z"/><path d="M8 13h8v-2H8v2z"/><path d="M17 7h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.71-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>',low_priority:'<path d="M14 5h8v2h-8z"/><path d="M14 10.5h8v2h-8z"/><path d="M14 16h8v2h-8z"/><path d="M2 11.5C2 15.08 4.92 18 8.5 18H9v2l3-3-3-3v2h-.5C6.02 16 4 13.98 4 11.5S6.02 7 8.5 7H12V5H8.5C4.92 5 2 7.92 2 11.5z"/>',mail:'<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>',markunread:'<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>',move_to_inbox:'<path d="M19 15h-4c0 1.66-1.35 3-3 3s-3-1.34-3-3H4.99V5H19v10zm0-12H4.99c-1.11 0-1.98.9-1.98 2L3 19c0 1.1.88 2 1.99 2H19c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M16 10h-2V7h-4v3H8l4 4 4-4z"/>',next_week:'<path d="M20 7h-4V5c0-.55-.22-1.05-.59-1.41C15.05 3.22 14.55 3 14 3h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5zm1 13.5l-1-1 3-3-3-3 1-1 4 4-4 4z"/>',redo:'<path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/>',remove:'<path d="M19 13H5v-2h14v2z"/>',remove_circle:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/>',remove_circle_outline:'<path d="M7 11v2h10v-2H7z"/><path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>',reply:'<path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>',reply_all:'<path d="M7 8V5l-7 7 7 7v-3l-4-4 4-4z"/><path d="M13 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>',report:'<path d="M15.73 3H8.27L3 8.27v7.46L8.27 21h7.46L21 15.73V8.27L15.73 3zM12 17.3c-.72 0-1.3-.58-1.3-1.3 0-.72.58-1.3 1.3-1.3.72 0 1.3.58 1.3 1.3 0 .72-.58 1.3-1.3 1.3zm1-4.3h-2V7h2v6z"/>',save:'<path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>',select_all:'<path d="M3 5h2V3c-1.1 0-2 .9-2 2z"/><path d="M3 13h2v-2H3v2z"/><path d="M7 21h2v-2H7v2z"/><path d="M3 9h2V7H3v2z"/><path d="M13 3h-2v2h2V3z"/><path d="M19 3v2h2c0-1.1-.9-2-2-2z"/><path d="M5 21v-2H3c0 1.1.9 2 2 2z"/><path d="M3 17h2v-2H3v2z"/><path d="M9 3H7v2h2V3z"/><path d="M11 21h2v-2h-2v2z"/><path d="M19 13h2v-2h-2v2z"/><path d="M19 21c1.1 0 2-.9 2-2h-2v2z"/><path d="M19 9h2V7h-2v2z"/><path d="M19 17h2v-2h-2v2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M15 5h2V3h-2v2z"/><path d="M9 9h6v6H9V9zm-2 8h10V7H7v10z"/>',send:'<path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>',sort:'<path d="M3 18h6v-2H3v2z"/><path d="M3 6v2h18V6H3z"/><path d="M3 13h12v-2H3v2z"/>',text_format:'<path d="M5 17v2h14v-2H5z"/><path d="M12 5.98L13.87 11h-3.74L12 5.98zM9.5 12.8h5l.9 2.2h2.1L12.75 4h-1.5L6.5 15h2.1l.9-2.2z"/>',unarchive:'<path d="M20.55 5.22l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.15.55L3.46 5.22C3.17 5.57 3 6.01 3 6.5V19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.49-.17-.93-.45-1.28zM12 9.5l5.5 5.5H14v2h-4v-2H6.5L12 9.5zM5.12 5l.82-1h12l.93 1H5.12z"/>',undo:'<path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>',weekend:'<path d="M21 10c-1.1 0-2 .9-2 2v3H5v-3c0-1.1-.9-2-2-2s-2 .9-2 2v5c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2v-5c0-1.1-.9-2-2-2z"/><path d="M18 5H6c-1.1 0-2 .9-2 2v2.15c1.16.41 2 1.51 2 2.82V14h12v-2.03c0-1.3.84-2.4 2-2.82V7c0-1.1-.9-2-2-2z"/>',access_alarms:'<path d="M22 5.72l-4.6-3.86-1.29 1.53 4.6 3.86L22 5.72z"/><path d="M7.88 3.39L6.6 1.86 2 5.71l1.29 1.53 4.59-3.85z"/><path d="M12.5 8H11v6l4.75 2.85.75-1.23-4-2.37V8z"/><path d="M12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm0-16c-4.97 0-9 4.03-9 9s4.02 9 9 9c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/>',access_alarm:'<path d="M22 5.72l-4.6-3.86-1.29 1.53 4.6 3.86L22 5.72z"/><path d="M7.88 3.39L6.6 1.86 2 5.71l1.29 1.53 4.59-3.85z"/><path d="M12.5 8H11v6l4.75 2.85.75-1.23-4-2.37V8z"/><path d="M12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm0-16c-4.97 0-9 4.03-9 9s4.02 9 9 9c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/>',access_time:'<path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.01-18C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>',add_alarm:'<path d="M7.88 3.39L6.6 1.86 2 5.71l1.29 1.53 4.59-3.85z"/><path d="M22 5.72l-4.6-3.86-1.29 1.53 4.6 3.86L22 5.72z"/><path d="M12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm0-16c-4.97 0-9 4.03-9 9s4.02 9 9 9c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/><path d="M13 9h-2v3H8v2h3v3h2v-3h3v-2h-3V9z"/>',airplanemode_on:'<path d="M10.18 9"/><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>',airplanemode_inactive:'<path d="M13 9V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5v3.68l7.83 7.83L21 16v-2l-8-5z"/><path d="M3 5.27l4.99 4.99L2 14v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-3.73L18.73 21 20 19.73 4.27 4 3 5.27z"/>',battery_charging_full:'<path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4zM11 20v-5.5H9L13 7v5.5h2L11 20z"/>',battery_full:'<path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/>',battery_std:'<path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/>',battery_unknown:'<path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4zm-2.72 13.95h-1.9v-1.9h1.9v1.9zm1.35-5.26s-.38.42-.67.71c-.48.48-.83 1.15-.83 1.6h-1.6c0-.83.46-1.52.93-2l.93-.94c.27-.27.44-.65.44-1.06 0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5H9c0-1.66 1.34-3 3-3s3 1.34 3 3c0 .66-.27 1.26-.7 1.69z"/>',bluetooth:'<path d="M17.71 7.71L12 2h-1v7.59L6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 11 14.41V22h1l5.71-5.71-4.3-4.29 4.3-4.29zM13 5.83l1.88 1.88L13 9.59V5.83zm1.88 10.46L13 18.17v-3.76l1.88 1.88z"/>',bluetooth_connected:'<path d="M14.88 16.29L13 18.17v-3.76l1.88 1.88zM13 5.83l1.88 1.88L13 9.59V5.83zm4.71 1.88L12 2h-1v7.59L6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 11 14.41V22h1l5.71-5.71-4.3-4.29 4.3-4.29z"/><path d="M19 10l-2 2 2 2 2-2-2-2z"/><path d="M7 12l-2-2-2 2 2 2 2-2z"/>',bluetooth_disabled:'<path d="M13 5.83l1.88 1.88-1.6 1.6 1.41 1.41 3.02-3.02L12 2h-1v5.03l2 2v-3.2z"/><path d="M13 18.17v-3.76l1.88 1.88L13 18.17zM5.41 4L4 5.41 10.59 12 5 17.59 6.41 19 11 14.41V22h1l4.29-4.29 2.3 2.29L20 18.59 5.41 4z"/>',bluetooth_searching:'<path d="M14.24 12.01l2.32 2.32c.28-.72.44-1.51.44-2.33 0-.82-.16-1.59-.43-2.31l-2.33 2.32z"/><path d="M19.53 6.71l-1.26 1.26c.63 1.21.98 2.57.98 4.02 0 1.45-.36 2.82-.98 4.02l1.2 1.2c.97-1.54 1.54-3.36 1.54-5.31-.01-1.89-.55-3.67-1.48-5.19z"/><path d="M12.88 16.29L11 18.17v-3.76l1.88 1.88zM11 5.83l1.88 1.88L11 9.59V5.83zm4.71 1.88L10 2H9v7.59L4.41 5 3 6.41 8.59 12 3 17.59 4.41 19 9 14.41V22h1l5.71-5.71-4.3-4.29 4.3-4.29z"/>',brightness_auto:'<path d="M10.85 12.65h2.3L12 9l-1.15 3.65zM20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69L23.31 12 20 8.69zM14.3 16l-.7-2h-3.2l-.7 2H7.8L11 7h2l3.2 9h-1.9z"/>',brightness_high:'<path d="M12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm8-9.31V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69L23.31 12 20 8.69z"/><path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"/>',brightness_low:'<path d="M20 15.31L23.31 12 20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69zM12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/>',brightness_medium:'<path d="M20 15.31L23.31 12 20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69zM12 18V6c3.31 0 6 2.69 6 6s-2.69 6-6 6z"/>',data_usage:'<path d="M13 2.05v3.03c3.39.49 6 3.39 6 6.92 0 .9-.18 1.75-.48 2.54l2.6 1.53c.56-1.24.88-2.62.88-4.07 0-5.18-3.95-9.45-9-9.95z"/><path d="M12 19c-3.87 0-7-3.13-7-7 0-3.53 2.61-6.43 6-6.92V2.05c-5.06.5-9 4.76-9 9.95 0 5.52 4.47 10 9.99 10 3.31 0 6.24-1.61 8.06-4.09l-2.6-1.53C16.17 17.98 14.21 19 12 19z"/>',developer_mode:'<path d="M7 5h10v2h2V3c0-1.1-.9-1.99-2-1.99L7 1c-1.1 0-2 .9-2 2v4h2V5z"/><path d="M15.41 16.59L20 12l-4.59-4.59L14 8.83 17.17 12 14 15.17l1.41 1.42z"/><path d="M10 15.17L6.83 12 10 8.83 8.59 7.41 4 12l4.59 4.59L10 15.17z"/><path d="M17 19H7v-2H5v4c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2v-4h-2v2z"/>',devices:'<path d="M4 6h18V4H4c-1.1 0-2 .9-2 2v11H0v3h14v-3H4V6z"/><path d="M22 17h-4v-7h4v7zm1-9h-6c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h6c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1z"/>',dvr:'<path d="M21 17H3V5h18v12zm0-14H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.1-.9-2-2-2z"/><path d="M7 8H5v2h2V8zm12 0H8v2h11V8z"/><path d="M7 12H5v2h2v-2zm12 0H8v2h11v-2z"/>',gps_fixed:'<path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"/><path d="M12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm8.94-8c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06z"/>',gps_not_fixed:'<path d="M20.94 11c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>',gps_off:'<path d="M20.94 11A8.994 8.994 0 0 0 13 3.06V1h-2v2.06c-1.13.12-2.19.46-3.16.97l1.5 1.5A6.995 6.995 0 0 1 19 12c0 .94-.19 1.84-.52 2.65l1.5 1.5c.5-.96.84-2.02.97-3.15H23v-2h-2.06z"/><path d="M16.27 17.54a6.995 6.995 0 0 1-9.81-9.81l9.81 9.81zM3 4.27l2.04 2.04A8.914 8.914 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06c1.77-.2 3.38-.91 4.69-1.98L19.73 21 21 19.73 4.27 3 3 4.27z"/>',graphic_eq:'<path d="M7 18h2V6H7v12z"/><path d="M11 22h2V2h-2v20z"/><path d="M3 14h2v-4H3v4z"/><path d="M15 18h2V6h-2v12z"/><path d="M19 10v4h2v-4h-2z"/>',location_disabled:'<path d="M20.94 11A8.994 8.994 0 0 0 13 3.06V1h-2v2.06c-1.13.12-2.19.46-3.16.97l1.5 1.5A6.995 6.995 0 0 1 19 12c0 .94-.19 1.84-.52 2.65l1.5 1.5c.5-.96.84-2.02.97-3.15H23v-2h-2.06z"/><path d="M16.27 17.54a6.995 6.995 0 0 1-9.81-9.81l9.81 9.81zM3 4.27l2.04 2.04A8.914 8.914 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06c1.77-.2 3.38-.91 4.69-1.98L19.73 21 21 19.73 4.27 3 3 4.27z"/>',location_searching:'<path d="M20.94 11c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>',network_cell:'<path fill-opacity=".3" d="M2 22h20V2z"/><path d="M17 7L2 22h15z"/>',network_wifi:'<path fill-opacity=".3" d="M12.01 21.49L23.64 7c-.45-.34-4.93-4-11.64-4C5.28 3 .81 6.66.36 7l11.63 14.49.01.01.01-.01z"/><path d="M3.53 10.95l8.46 10.54.01.01.01-.01 8.46-10.54C20.04 10.62 16.81 8 12 8c-4.81 0-8.04 2.62-8.47 2.95z"/>',nfc:'<path d="M20 20H4V4h16v16zm0-18H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/><path d="M18 6h-5c-1.1 0-2 .9-2 2v2.28c-.6.35-1 .98-1 1.72 0 1.1.9 2 2 2s2-.9 2-2c0-.74-.4-1.38-1-1.72V8h3v8H8V8h2V6H6v12h12V6z"/>',screen_lock_landscape:'<path d="M19 17H5V7h14v10zm2-12H3c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2z"/><path d="M10.8 10c0-.66.54-1.2 1.2-1.2.66 0 1.2.54 1.2 1.2v1h-2.4v-1zm-.8 6h4c.55 0 1-.45 1-1v-3c0-.55-.45-1-1-1v-1c0-1.11-.9-2-2-2-1.11 0-2 .9-2 2v1c-.55 0-1 .45-1 1v3c0 .55.45 1 1 1z"/>',screen_lock_portrait:'<path d="M10.8 10c0-.66.54-1.2 1.2-1.2.66 0 1.2.54 1.2 1.2v1h-2.4v-1zm-.8 6h4c.55 0 1-.45 1-1v-3c0-.55-.45-1-1-1v-1c0-1.11-.9-2-2-2-1.11 0-2 .9-2 2v1c-.55 0-1 .45-1 1v3c0 .55.45 1 1 1z"/><path d="M17 19H7V5h10v14zm0-18H7c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/>',screen_lock_rotation:'<path d="M23.25 12.77l-2.57-2.57-1.41 1.41 2.22 2.22-5.66 5.66L4.51 8.17l5.66-5.66 2.1 2.1 1.41-1.41L11.23.75c-.59-.59-1.54-.59-2.12 0L2.75 7.11c-.59.59-.59 1.54 0 2.12l12.02 12.02c.59.59 1.54.59 2.12 0l6.36-6.36c.59-.59.59-1.54 0-2.12z"/><path d="M8.47 20.48C5.2 18.94 2.86 15.76 2.5 12H1c.51 6.16 5.66 11 11.95 11l.66-.03-3.81-3.82-1.33 1.33z"/><path d="M16.8 2.5c0-.94.76-1.7 1.7-1.7s1.7.76 1.7 1.7V3h-3.4v-.5zM16 9h5c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1v-.5C21 1.12 19.88 0 18.5 0S16 1.12 16 2.5V3c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1z"/>',screen_rotation:'<path d="M16.48 2.52c3.27 1.55 5.61 4.72 5.97 8.48h1.5C23.44 4.84 18.29 0 12 0l-.66.03 3.81 3.81 1.33-1.32z"/><path d="M14.83 21.19L2.81 9.17l6.36-6.36 12.02 12.02-6.36 6.36zm-4.6-19.44c-.59-.59-1.54-.59-2.12 0L1.75 8.11c-.59.59-.59 1.54 0 2.12l12.02 12.02c.59.59 1.54.59 2.12 0l6.36-6.36c.59-.59.59-1.54 0-2.12L10.23 1.75z"/><path d="M7.52 21.48C4.25 19.94 1.91 16.76 1.55 13H.05C.56 19.16 5.71 24 12 24l.66-.03-3.81-3.81-1.33 1.32z"/>',sd_storage:'<path d="M18 2h-8L4.02 8 4 20c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-6 6h-2V4h2v4zm3 0h-2V4h2v4zm3 0h-2V4h2v4z"/>',settings_system_daydream:'<path d="M9 16h6.5c1.38 0 2.5-1.12 2.5-2.5S16.88 11 15.5 11h-.05c-.24-1.69-1.69-3-3.45-3-1.4 0-2.6.83-3.16 2.02h-.16C7.17 10.18 6 11.45 6 13c0 1.66 1.34 3 3 3z"/><path d="M21 19.01H3V4.99h18v14.02zM21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',signal_cellular_4_bar:'<path d="M2 22h20V2z"/>',signal_cellular_connected_no_internet_4_bar:'<path d="M20 18h2v-8h-2v8z"/><path d="M20 22h2v-2h-2v2z"/><path d="M2 22h16V8h4V2L2 22z"/>',signal_cellular_no_sim:'<path d="M18.99 5c0-1.1-.89-2-1.99-2h-7L7.66 5.34 19 16.68 18.99 5z"/><path d="M3.65 3.88L2.38 5.15 5 7.77V19c0 1.1.9 2 2 2h10.01c.35 0 .67-.1.96-.26l1.88 1.88 1.27-1.27L3.65 3.88z"/>',signal_cellular_null:'<path d="M20 6.83V20H6.83L20 6.83M22 2L2 22h20V2z"/>',signal_cellular_off:'<path d="M21 1l-8.59 8.59L21 18.18V1z"/><path d="M4.77 4.5L3.5 5.77l6.36 6.36L1 21h17.73l2 2L22 21.73 4.77 4.5z"/>',signal_wifi_4_bar:'<path d="M12.01 21.49L23.64 7c-.45-.34-4.93-4-11.64-4C5.28 3 .81 6.66.36 7l11.63 14.49.01.01.01-.01z"/>',signal_wifi_4_bar_lock:'<path d="M22 16h-3v-1.5c0-.8.7-1.5 1.5-1.5s1.5.7 1.5 1.5V16zm1 0v-1.5c0-1.4-1.1-2.5-2.5-2.5S18 13.1 18 14.5V16c-.5 0-1 .5-1 1v4c0 .5.5 1 1 1h5c.5 0 1-.5 1-1v-4c0-.5-.5-1-1-1z"/><path d="M15.5 14.5c0-2.8 2.2-5 5-5 .4 0 .7 0 1 .1L23.6 7c-.4-.3-4.9-4-11.6-4C5.3 3 .8 6.7.4 7L12 21.5l3.5-4.4v-2.6z"/>',signal_wifi_off:'<path d="M23.64 7c-.45-.34-4.93-4-11.64-4-1.5 0-2.89.19-4.15.48L18.18 13.8 23.64 7z"/><path d="M17.04 15.22L3.27 1.44 2 2.72l2.05 2.06C1.91 5.76.59 6.82.36 7l11.63 14.49.01.01.01-.01 3.9-4.86 3.32 3.32 1.27-1.27-3.46-3.46z"/>',storage:'<path d="M4 17h2v2H4v-2zm-2 3h20v-4H2v4z"/><path d="M6 7H4V5h2v2zM2 4v4h20V4H2z"/><path d="M4 11h2v2H4v-2zm-2 3h20v-4H2v4z"/>',usb:'<path d="M15 7v4h1v2h-3V5h2l-3-4-3 4h2v8H8v-2.07c.7-.37 1.2-1.08 1.2-1.93 0-1.21-.99-2.2-2.2-2.2-1.21 0-2.2.99-2.2 2.2 0 .85.5 1.56 1.2 1.93V13c0 1.11.89 2 2 2h3v3.05c-.71.37-1.2 1.1-1.2 1.95 0 1.22.99 2.2 2.2 2.2 1.21 0 2.2-.98 2.2-2.2 0-.85-.49-1.58-1.2-1.95V15h3c1.11 0 2-.89 2-2v-2h1V7h-4z"/>',wallpaper:'<path d="M4 4h7V2H4c-1.1 0-2 .9-2 2v7h2V4z"/><path d="M10 13l-4 5h12l-3-4-2.03 2.71L10 13z"/><path d="M17 8.5c0-.83-.67-1.5-1.5-1.5S14 7.67 14 8.5s.67 1.5 1.5 1.5S17 9.33 17 8.5z"/><path d="M20 2h-7v2h7v7h2V4c0-1.1-.9-2-2-2z"/><path d="M20 20h-7v2h7c1.1 0 2-.9 2-2v-7h-2v7z"/><path d="M4 13H2v7c0 1.1.9 2 2 2h7v-2H4v-7z"/>',wifi_lock:'<path d="M20.5 9.5c.28 0 .55.04.81.08L24 6c-3.34-2.51-7.5-4-12-4S3.34 3.49 0 6l12 16 3.5-4.67V14.5c0-2.76 2.24-5 5-5z"/><path d="M22 16h-3v-1.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V16zm1 0v-1.5c0-1.38-1.12-2.5-2.5-2.5S18 13.12 18 14.5V16c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h5c.55 0 1-.45 1-1v-4c0-.55-.45-1-1-1z"/>',wifi_tethering:'<path d="M12 11c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M18 13c0-3.31-2.69-6-6-6s-6 2.69-6 6c0 2.22 1.21 4.15 3 5.19l1-1.74c-1.19-.7-2-1.97-2-3.45 0-2.21 1.79-4 4-4s4 1.79 4 4c0 1.48-.81 2.75-2 3.45l1 1.74c1.79-1.04 3-2.97 3-5.19z"/><path d="M12 3C6.48 3 2 7.48 2 13c0 3.7 2.01 6.92 4.99 8.65l1-1.73C5.61 18.53 4 15.96 4 13c0-4.42 3.58-8 8-8s8 3.58 8 8c0 2.96-1.61 5.53-4 6.92l1 1.73c2.99-1.73 5-4.95 5-8.65 0-5.52-4.48-10-10-10z"/>',attach_file:'<path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>',attach_money:'<path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>',border_all:'<path d="M3 3v18h18V3H3zm8 16H5v-6h6v6zm0-8H5V5h6v6zm8 8h-6v-6h6v6zm0-8h-6V5h6v6z"/>',border_bottom:'<path d="M9 11H7v2h2v-2z"/><path d="M13 15h-2v2h2v-2z"/><path d="M9 3H7v2h2V3z"/><path d="M13 11h-2v2h2v-2z"/><path d="M5 3H3v2h2V3z"/><path d="M13 7h-2v2h2V7z"/><path d="M17 11h-2v2h2v-2z"/><path d="M13 3h-2v2h2V3z"/><path d="M17 3h-2v2h2V3z"/><path d="M19 13h2v-2h-2v2z"/><path d="M19 17h2v-2h-2v2z"/><path d="M5 7H3v2h2V7z"/><path d="M19 3v2h2V3h-2z"/><path d="M19 9h2V7h-2v2z"/><path d="M5 11H3v2h2v-2z"/><path d="M3 21h18v-2H3v2z"/><path d="M5 15H3v2h2v-2z"/>',border_clear:'<path d="M7 5h2V3H7v2z"/><path d="M7 13h2v-2H7v2z"/><path d="M7 21h2v-2H7v2z"/><path d="M11 17h2v-2h-2v2z"/><path d="M11 21h2v-2h-2v2z"/><path d="M3 21h2v-2H3v2z"/><path d="M3 17h2v-2H3v2z"/><path d="M3 13h2v-2H3v2z"/><path d="M3 9h2V7H3v2z"/><path d="M3 5h2V3H3v2z"/><path d="M11 13h2v-2h-2v2z"/><path d="M19 17h2v-2h-2v2z"/><path d="M19 13h2v-2h-2v2z"/><path d="M19 21h2v-2h-2v2z"/><path d="M19 9h2V7h-2v2z"/><path d="M11 9h2V7h-2v2z"/><path d="M19 3v2h2V3h-2z"/><path d="M11 5h2V3h-2v2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M15 13h2v-2h-2v2z"/><path d="M15 5h2V3h-2v2z"/>',border_color:'<path d="M17.75 7L14 3.25l-10 10V17h3.75l10-10zm2.96-2.96c.39-.39.39-1.02 0-1.41L18.37.29c-.39-.39-1.02-.39-1.41 0L15 2.25 18.75 6l1.96-1.96z"/><path fill-opacity=".36" d="M0 20h24v4H0z"/>',border_horizontal:'<path d="M3 21h2v-2H3v2z"/><path d="M5 7H3v2h2V7z"/><path d="M3 17h2v-2H3v2z"/><path d="M7 21h2v-2H7v2z"/><path d="M5 3H3v2h2V3z"/><path d="M9 3H7v2h2V3z"/><path d="M17 3h-2v2h2V3z"/><path d="M13 7h-2v2h2V7z"/><path d="M13 3h-2v2h2V3z"/><path d="M19 17h2v-2h-2v2z"/><path d="M11 21h2v-2h-2v2z"/><path d="M3 13h18v-2H3v2z"/><path d="M19 3v2h2V3h-2z"/><path d="M19 9h2V7h-2v2z"/><path d="M11 17h2v-2h-2v2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M19 21h2v-2h-2v2z"/>',border_inner:'<path d="M3 21h2v-2H3v2z"/><path d="M7 21h2v-2H7v2z"/><path d="M5 7H3v2h2V7z"/><path d="M3 17h2v-2H3v2z"/><path d="M9 3H7v2h2V3z"/><path d="M5 3H3v2h2V3z"/><path d="M17 3h-2v2h2V3z"/><path d="M19 9h2V7h-2v2z"/><path d="M19 3v2h2V3h-2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M13 3h-2v8H3v2h8v8h2v-8h8v-2h-8V3z"/><path d="M19 21h2v-2h-2v2z"/><path d="M19 17h2v-2h-2v2z"/>',border_left:'<path d="M11 21h2v-2h-2v2z"/><path d="M11 17h2v-2h-2v2z"/><path d="M11 5h2V3h-2v2z"/><path d="M11 9h2V7h-2v2z"/><path d="M11 13h2v-2h-2v2z"/><path d="M7 21h2v-2H7v2z"/><path d="M7 5h2V3H7v2z"/><path d="M7 13h2v-2H7v2z"/><path d="M3 21h2V3H3v18z"/><path d="M19 9h2V7h-2v2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M19 17h2v-2h-2v2z"/><path d="M19 3v2h2V3h-2z"/><path d="M19 13h2v-2h-2v2z"/><path d="M19 21h2v-2h-2v2z"/><path d="M15 13h2v-2h-2v2z"/><path d="M15 5h2V3h-2v2z"/>',border_outer:'<path d="M13 7h-2v2h2V7z"/><path d="M13 11h-2v2h2v-2z"/><path d="M17 11h-2v2h2v-2z"/><path d="M19 19H5V5h14v14zM3 3v18h18V3H3z"/><path d="M13 15h-2v2h2v-2z"/><path d="M9 11H7v2h2v-2z"/>',border_right:'<path d="M7 21h2v-2H7v2z"/><path d="M3 5h2V3H3v2z"/><path d="M7 5h2V3H7v2z"/><path d="M7 13h2v-2H7v2z"/><path d="M3 21h2v-2H3v2z"/><path d="M11 21h2v-2h-2v2z"/><path d="M3 13h2v-2H3v2z"/><path d="M3 17h2v-2H3v2z"/><path d="M3 9h2V7H3v2z"/><path d="M11 17h2v-2h-2v2z"/><path d="M15 13h2v-2h-2v2z"/><path d="M19 3v18h2V3h-2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M15 5h2V3h-2v2z"/><path d="M11 13h2v-2h-2v2z"/><path d="M11 5h2V3h-2v2z"/><path d="M11 9h2V7h-2v2z"/>',border_style:'<path d="M15 21h2v-2h-2v2z"/><path d="M19 21h2v-2h-2v2z"/><path d="M7 21h2v-2H7v2z"/><path d="M11 21h2v-2h-2v2z"/><path d="M19 17h2v-2h-2v2z"/><path d="M19 13h2v-2h-2v2z"/><path d="M3 3v18h2V5h16V3H3z"/><path d="M19 9h2V7h-2v2z"/>',border_top:'<path d="M7 21h2v-2H7v2z"/><path d="M7 13h2v-2H7v2z"/><path d="M11 13h2v-2h-2v2z"/><path d="M11 21h2v-2h-2v2z"/><path d="M3 17h2v-2H3v2z"/><path d="M3 21h2v-2H3v2z"/><path d="M3 13h2v-2H3v2z"/><path d="M3 9h2V7H3v2z"/><path d="M11 17h2v-2h-2v2z"/><path d="M19 9h2V7h-2v2z"/><path d="M19 13h2v-2h-2v2z"/><path d="M3 3v2h18V3H3z"/><path d="M19 17h2v-2h-2v2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M11 9h2V7h-2v2z"/><path d="M19 21h2v-2h-2v2z"/><path d="M15 13h2v-2h-2v2z"/>',
border_vertical:'<path d="M3 9h2V7H3v2z"/><path d="M3 5h2V3H3v2z"/><path d="M7 21h2v-2H7v2z"/><path d="M7 13h2v-2H7v2z"/><path d="M3 13h2v-2H3v2z"/><path d="M3 21h2v-2H3v2z"/><path d="M3 17h2v-2H3v2z"/><path d="M7 5h2V3H7v2z"/><path d="M19 17h2v-2h-2v2z"/><path d="M11 21h2V3h-2v18z"/><path d="M19 21h2v-2h-2v2z"/><path d="M19 13h2v-2h-2v2z"/><path d="M19 3v2h2V3h-2z"/><path d="M19 9h2V7h-2v2z"/><path d="M15 5h2V3h-2v2z"/><path d="M15 21h2v-2h-2v2z"/><path d="M15 13h2v-2h-2v2z"/>',bubble_chart:'<circle cx="7.2" cy="14.4" r="3.2"/><circle cx="14.8" cy="18" r="2"/><circle cx="15.2" cy="8.8" r="4.8"/>',drag_handle:'<path d="M20 9H4v2h16V9z"/><path d="M4 15h16v-2H4v2z"/>',format_align_center:'<path d="M7 15v2h10v-2H7z"/><path d="M3 21h18v-2H3v2z"/><path d="M3 13h18v-2H3v2z"/><path d="M7 7v2h10V7H7z"/><path d="M3 3v2h18V3H3z"/>',format_align_justify:'<path d="M3 21h18v-2H3v2z"/><path d="M3 17h18v-2H3v2z"/><path d="M3 13h18v-2H3v2z"/><path d="M3 9h18V7H3v2z"/><path d="M3 3v2h18V3H3z"/>',format_align_left:'<path d="M15 15H3v2h12v-2z"/><path d="M15 7H3v2h12V7z"/><path d="M3 13h18v-2H3v2z"/><path d="M3 21h18v-2H3v2z"/><path d="M3 3v2h18V3H3z"/>',format_align_right:'<path d="M3 21h18v-2H3v2z"/><path d="M9 17h12v-2H9v2z"/><path d="M3 13h18v-2H3v2z"/><path d="M9 9h12V7H9v2z"/><path d="M3 3v2h18V3H3z"/>',format_bold:'<path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/>',format_clear:'<path d="M3.27 5L2 6.27l6.97 6.97L6.5 19h3l1.57-3.66L16.73 21 18 19.73 3.55 5.27 3.27 5z"/><path d="M6 5v.18L8.82 8h2.4l-.72 1.68 2.1 2.1L14.21 8H20V5H6z"/>',format_color_fill:'<path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15c-.59.59-.59 1.54 0 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z"/><path fill-opacity=".36" d="M0 20h24v4H0z"/>',format_color_reset:'<path d="M18 14c0-4-6-10.8-6-10.8s-1.33 1.51-2.73 3.52l8.59 8.59c.09-.42.14-.86.14-1.31z"/><path d="M17.12 17.12L12.5 12.5 5.27 5.27 4 6.55l3.32 3.32C6.55 11.32 6 12.79 6 14c0 3.31 2.69 6 6 6 1.52 0 2.9-.57 3.96-1.5l2.63 2.63 1.27-1.27-2.74-2.74z"/>',format_color_text:'<path fill-opacity=".36" d="M0 20h24v4H0z"/><path d="M11 3L5.5 17h2.25l1.12-3h6.25l1.12 3h2.25L13 3h-2zm-1.38 9L12 5.67 14.38 12H9.62z"/>',format_indent_decrease:'<path d="M11 17h10v-2H11v2z"/><path d="M3 12l4 4V8l-4 4z"/><path d="M3 21h18v-2H3v2z"/><path d="M3 3v2h18V3H3z"/><path d="M11 9h10V7H11v2z"/><path d="M11 13h10v-2H11v2z"/>',format_indent_increase:'<path d="M3 21h18v-2H3v2z"/><path d="M3 8v8l4-4-4-4z"/><path d="M11 17h10v-2H11v2z"/><path d="M3 3v2h18V3H3z"/><path d="M11 9h10V7H11v2z"/><path d="M11 13h10v-2H11v2z"/>',format_italic:'<path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z"/>',format_line_spacing:'<path d="M6 7h2.5L5 3.5 1.5 7H4v10H1.5L5 20.5 8.5 17H6V7z"/><path d="M10 5v2h12V5H10z"/><path d="M10 19h12v-2H10v2z"/><path d="M10 13h12v-2H10v2z"/>',format_list_bulleted:'<path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/><path d="M4 4.5c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5z"/><path d="M4 16.67c-.74 0-1.33.6-1.33 1.33 0 .73.6 1.33 1.33 1.33.73 0 1.33-.6 1.33-1.33 0-.73-.59-1.33-1.33-1.33z"/><path d="M7 19h14v-2H7v2z"/><path d="M7 13h14v-2H7v2z"/><path d="M7 5v2h14V5H7z"/>',format_list_numbered:'<path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1z"/><path d="M3 8h1V4H2v1h1v3z"/><path d="M2 11h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1z"/><path d="M7 5v2h14V5H7z"/><path d="M7 19h14v-2H7v2z"/><path d="M7 13h14v-2H7v2z"/>',format_paint:'<path d="M18 4V3c0-.55-.45-1-1-1H5c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h12c.55 0 1-.45 1-1V6h1v4H9v11c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-9h8V4h-3z"/>',format_quote:'<path d="M6 17h3l2-4V7H5v6h3z"/><path d="M14 17h3l2-4V7h-6v6h3z"/>',format_shapes:'<path d="M19 5V3h2v2h-2zm2 16h-2v-2h2v2zm-4-2H7v-2H5V7h2V5h10v2h2v10h-2v2zM5 21H3v-2h2v2zM3 3h2v2H3V3zm20 4V1h-6v2H7V1H1v6h2v10H1v6h6v-2h10v2h6v-6h-2V7h2z"/><path d="M10.69 12.74h2.61L12 8.91l-1.31 3.83zM13.73 14h-3.49l-.73 2H7.89l3.4-9h1.4l3.41 9h-1.63l-.74-2z"/>',format_size:'<path d="M9 4v3h5v12h3V7h5V4H9z"/><path d="M3 12h3v7h3v-7h3V9H3v3z"/>',format_strikethrough:'<path d="M10 19h4v-3h-4v3z"/><path d="M5 4v3h5v3h4V7h5V4H5z"/><path d="M3 14h18v-2H3v2z"/>',format_textdirection_l_to_r:'<path d="M9 10v5h2V4h2v11h2V4h2V2H9C6.79 2 5 3.79 5 6s1.79 4 4 4z"/><path d="M21 18l-4-4v3H5v2h12v3l4-4z"/>',format_textdirection_r_to_l:'<path d="M10 10v5h2V4h2v11h2V4h2V2h-8C7.79 2 6 3.79 6 6s1.79 4 4 4z"/><path d="M8 17v-3l-4 4 4 4v-3h12v-2H8z"/>',format_underline:'<path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6z"/><path d="M5 19v2h14v-2H5z"/>',functions:'<path d="M18 4H6v2l6.5 6L6 18v2h12v-3h-7l5-5-5-5h7z"/>',highlight:'<path d="M6 14l3 3v5h6v-5l3-3V9H6z"/><path d="M11 2h2v3h-2z"/><path d="M3.5 5.875L4.914 4.46l2.12 2.122L5.62 7.997z"/><path d="M16.96 6.585l2.123-2.12 1.414 1.414L18.375 8z"/>',insert_chart:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>',insert_comment:'<path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>',insert_drive_file:'<path d="M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z"/>',insert_emoticon:'<path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.01-18C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2z"/><path d="M15.5 11c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5z"/><path d="M8.5 11c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11z"/><path d="M12 17.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>',insert_invitation:'<path d="M17 12h-5v5h5v-5z"/><path d="M19 19H5V8h14v11zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2z"/>',insert_link:'<path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1z"/><path d="M8 13h8v-2H8v2z"/><path d="M17 7h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.71-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>',insert_photo:'<path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>',linear_scale:'<path d="M19.5 9.5c-1.03 0-1.9.62-2.29 1.5h-2.92c-.39-.88-1.26-1.5-2.29-1.5s-1.9.62-2.29 1.5H6.79c-.39-.88-1.26-1.5-2.29-1.5C3.12 9.5 2 10.62 2 12s1.12 2.5 2.5 2.5c1.03 0 1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5s1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5 1.38 0 2.5-1.12 2.5-2.5s-1.12-2.5-2.5-2.5z"/>',merge_type:'<path d="M17 20.41L18.41 19 15 15.59 13.59 17 17 20.41z"/><path d="M7.5 8H11v5.59L5.59 19 7 20.41l6-6V8h3.5L12 3.5 7.5 8z"/>',mode_comment:'<path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z"/>',mode_edit:'<path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"/><path d="M20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>',monetization_on:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>',money_off:'<path d="M12.5 6.9c1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-.53.12-1.03.3-1.48.54l1.47 1.47c.41-.17.91-.27 1.51-.27z"/><path d="M5.33 4.06L4.06 5.33 7.5 8.77c0 2.08 1.56 3.21 3.91 3.91l3.51 3.51c-.34.48-1.05.91-2.42.91-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c.96-.18 1.82-.55 2.45-1.12l2.22 2.22 1.27-1.27L5.33 4.06z"/>',multiline_chart:'<path d="M22 6.92l-1.41-1.41-2.85 3.21C15.68 6.4 12.83 5 9.61 5 6.72 5 4.07 6.16 2 8l1.42 1.42C5.12 7.93 7.27 7 9.61 7c2.74 0 5.09 1.26 6.77 3.24l-2.88 3.24-4-4L2 16.99l1.5 1.5 6-6.01 4 4 4.05-4.55c.75 1.35 1.25 2.9 1.44 4.55H21c-.22-2.3-.95-4.39-2.04-6.14L22 6.92z"/>',pie_chart:'<path d="M11 2v20c-5.07-.5-9-4.79-9-10s3.93-9.5 9-10z"/><path d="M13.03 2v8.99H22c-.47-4.74-4.24-8.52-8.97-8.99z"/><path d="M13.03 13.01V22c4.74-.47 8.5-4.25 8.97-8.99h-8.97z"/>',pie_chart_outline:'<path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm1 2.07c3.61.45 6.48 3.33 6.93 6.93H13V4.07zM4 12c0-4.06 3.07-7.44 7-7.93v15.87c-3.93-.5-7-3.88-7-7.94zm9 7.93V13h6.93c-.45 3.61-3.32 6.48-6.93 6.93z"/>',publish:'<path d="M5 4v2h14V4H5z"/><path d="M5 14h4v6h6v-6h4l-7-7-7 7z"/>',short_text:'<path d="M4 9h16v2H4z"/><path d="M4 13h10v2H4z"/>',show_chart:'<path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>',space_bar:'<path d="M18 9v4H6V9H4v6h16V9z"/>',strikethrough_s:'<path d="M7.24 8.75c-.26-.48-.39-1.03-.39-1.67 0-.61.13-1.16.4-1.67.26-.5.63-.93 1.11-1.29a5.73 5.73 0 0 1 1.7-.83c.66-.19 1.39-.29 2.18-.29.81 0 1.54.11 2.21.34.66.22 1.23.54 1.69.94.47.4.83.88 1.08 1.43.25.55.38 1.15.38 1.81h-3.01c0-.31-.05-.59-.15-.85-.09-.27-.24-.49-.44-.68-.2-.19-.45-.33-.75-.44-.3-.1-.66-.16-1.06-.16-.39 0-.74.04-1.03.13-.29.09-.53.21-.72.36-.19.16-.34.34-.44.55-.1.21-.15.43-.15.66 0 .48.25.88.74 1.21.38.25.77.48 1.41.7H7.39c-.05-.08-.11-.17-.15-.25z"/><path d="M21 12v-2H3v2h9.62c.18.07.4.14.55.2.37.17.66.34.87.51.21.17.35.36.43.57.07.2.11.43.11.69 0 .23-.05.45-.14.66-.09.2-.23.38-.42.53-.19.15-.42.26-.71.35-.29.08-.63.13-1.01.13-.43 0-.83-.04-1.18-.13s-.66-.23-.91-.42c-.25-.19-.45-.44-.59-.75-.14-.31-.25-.76-.25-1.21H6.4c0 .55.08 1.13.24 1.58.16.45.37.85.65 1.21.28.35.6.66.98.92.37.26.78.48 1.22.65.44.17.9.3 1.38.39.48.08.96.13 1.44.13.8 0 1.53-.09 2.18-.28.65-.19 1.21-.45 1.67-.79.46-.34.82-.77 1.07-1.27.25-.5.38-1.07.38-1.71 0-.6-.1-1.14-.31-1.61-.05-.11-.11-.23-.17-.33H21z"/>',text_fields:'<path d="M2.5 4v3h5v12h3V7h5V4h-13z"/><path d="M21.5 9h-9v3h3v7h3v-7h3V9z"/>',title:'<path d="M5 4v3h5.5v12h3V7H19V4z"/>',vertical_align_bottom:'<path d="M16 13h-3V3h-2v10H8l4 4 4-4z"/><path d="M4 19v2h16v-2H4z"/>',vertical_align_center:'<path d="M8 19h3v4h2v-4h3l-4-4-4 4z"/><path d="M16 5h-3V1h-2v4H8l4 4 4-4z"/><path d="M4 11v2h16v-2H4z"/>',vertical_align_top:'<path d="M8 11h3v10h2V11h3l-4-4-4 4z"/><path d="M4 3v2h16V3H4z"/>',wrap_text:'<path d="M4 19h6v-2H4v2z"/><path d="M20 5H4v2h16V5z"/><path d="M17 11H4v2h13.25c1.1 0 2 .9 2 2s-.9 2-2 2H15v-2l-3 3 3 3v-2h2c2.21 0 4-1.79 4-4s-1.79-4-4-4z"/>',attachment:'<path d="M7.5 18C4.46 18 2 15.54 2 12.5S4.46 7 7.5 7H18c2.21 0 4 1.79 4 4s-1.79 4-4 4H9.5C8.12 15 7 13.88 7 12.5S8.12 10 9.5 10H17v1.5H9.5c-.55 0-1 .45-1 1s.45 1 1 1H18c1.38 0 2.5-1.12 2.5-2.5S19.38 8.5 18 8.5H7.5c-2.21 0-4 1.79-4 4s1.79 4 4 4H17V18H7.5z"/>',cloud:'<path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>',cloud_circle:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.5 14H8c-1.66 0-3-1.34-3-3s1.34-3 3-3l.14.01C8.58 8.28 10.13 7 12 7c2.21 0 4 1.79 4 4h.5c1.38 0 2.5 1.12 2.5 2.5S17.88 16 16.5 16z"/>',cloud_done:'<path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM10 17l-3.5-3.5 1.41-1.41L10 14.17 15.18 9l1.41 1.41L10 17z"/>',cloud_download:'<path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM17 13l-5 5-5-5h3V9h4v4h3z"/>',cloud_off:'<path d="M19.35 10.04A7.49 7.49 0 0 0 12 4c-1.48 0-2.85.43-4.01 1.17l1.46 1.46a5.497 5.497 0 0 1 8.05 4.87v.5H19c1.66 0 3 1.34 3 3 0 1.13-.64 2.11-1.56 2.62l1.45 1.45C23.16 18.16 24 16.68 24 15c0-2.64-2.05-4.78-4.65-4.96z"/><path d="M7.73 10l8 8H6c-2.21 0-4-1.79-4-4s1.79-4 4-4h1.73zM3 5.27l2.75 2.74C2.56 8.15 0 10.77 0 14c0 3.31 2.69 6 6 6h11.73l2 2L21 20.73 4.27 4 3 5.27z"/>',cloud_queue:'<path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM19 18H6c-2.21 0-4-1.79-4-4s1.79-4 4-4h.71C7.37 7.69 9.48 6 12 6c3.04 0 5.5 2.46 5.5 5.5v.5H19c1.66 0 3 1.34 3 3s-1.34 3-3 3z"/>',cloud_upload:'<path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>',create_new_folder:'<path d="M20 6h-8l-2-2H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-1 8h-3v3h-2v-3h-3v-2h3V9h2v3h3v2z"/>',file_download:'<path d="M19 9h-4V3H9v6H5l7 7 7-7z"/><path d="M5 18v2h14v-2H5z"/>',file_upload:'<path d="M9 16h6v-6h4l-7-7-7 7h4z"/><path d="M5 18h14v2H5z"/>',folder:'<path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>',folder_open:'<path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z"/>',folder_shared:'<path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-5 3c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm4 8h-8v-1c0-1.33 2.67-2 4-2s4 .67 4 2v1z"/>',cast:'<path d="M21 3H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M1 18v3h3c0-1.66-1.34-3-3-3z"/><path d="M1 14v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7z"/><path d="M1 10v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11z"/>',cast_connected:'<path d="M1 18v3h3c0-1.66-1.34-3-3-3z"/><path d="M1 14v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7z"/><path d="M19 7H5v1.63c3.96 1.28 7.09 4.41 8.37 8.37H19V7z"/><path d="M1 10v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11z"/><path d="M21 3H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',computer:'<path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>',desktop_mac:'<path d="M21 2H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h7l-2 3v1h8v-1l-2-3h7c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 12H3V4h18v10z"/>',desktop_windows:'<path d="M21 2H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h7v2H8v2h8v-2h-2v-2h7c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H3V4h18v12z"/>',developer_dashboard:'<path d="M18 19H4V5h14v14zm4-10V7h-2V5c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-2h2v-2h-2v-2h2v-2h-2V9h2z"/><path d="M6 13h5v4H6z"/><path d="M12 7h4v3h-4z"/><path d="M6 7h5v5H6z"/><path d="M12 11h4v6h-4z"/>',device_hub:'<path d="M17 16l-4-4V8.82C14.16 8.4 15 7.3 15 6c0-1.66-1.34-3-3-3S9 4.34 9 6c0 1.3.84 2.4 2 2.82V12l-4 4H3v5h5v-3.05l4-4.2 4 4.2V21h5v-5h-4z"/>',devices_other:'<path d="M3 6h18V4H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4v-2H3V6z"/><path d="M11 17.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm2-5.5H9v1.78c-.61.55-1 1.33-1 2.22 0 .89.39 1.67 1 2.22V20h4v-1.78c.61-.55 1-1.34 1-2.22 0-.88-.39-1.67-1-2.22V12z"/><path d="M21 18h-4v-8h4v8zm1-10h-6c-.5 0-1 .5-1 1v10c0 .5.5 1 1 1h6c.5 0 1-.5 1-1V9c0-.5-.5-1-1-1z"/>',dock:'<path d="M8 23h8v-2H8v2z"/><path d="M16 15H8V5h8v10zm0-13.99L8 1c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99z"/>',gamepad:'<path d="M15 7.5V2H9v5.5l3 3 3-3z"/><path d="M7.5 9H2v6h5.5l3-3-3-3z"/><path d="M9 16.5V22h6v-5.5l-3-3-3 3z"/><path d="M16.5 9l-3 3 3 3H22V9h-5.5z"/>',headset:'<path d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z"/>',headset_mic:'<path d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h4v1h-7v2h6c1.66 0 3-1.34 3-3V10c0-4.97-4.03-9-9-9z"/>',keyboard:'<path d="M20 5H4c-1.1 0-1.99.9-1.99 2L2 17c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 2H5v-2h2v2zm0-3H5V8h2v2zm9 7H8v-2h8v2zm0-4h-2v-2h2v2zm0-3h-2V8h2v2zm3 3h-2v-2h2v2zm0-3h-2V8h2v2z"/>',keyboard_arrow_down:'<path d="M7.41 7.84L12 12.42l4.59-4.58L18 9.25l-6 6-6-6z"/>',keyboard_arrow_left:'<path d="M15.41 16.09l-4.58-4.59 4.58-4.59L14 5.5l-6 6 6 6z"/>',keyboard_arrow_right:'<path d="M8.59 16.34l4.58-4.59-4.58-4.59L10 5.75l6 6-6 6z"/>',keyboard_arrow_up:'<path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>',keyboard_backspace:'<path d="M21 11H6.83l3.58-3.59L9 6l-6 6 6 6 1.41-1.41L6.83 13H21z"/>',keyboard_capslock:'<path d="M12 8.41L16.59 13 18 11.59l-6-6-6 6L7.41 13 12 8.41z"/><path d="M6 18h12v-2H6v2z"/>',keyboard_hide:'<path d="M19 8h-2V6h2v2zm0 3h-2V9h2v2zm-3-3h-2V6h2v2zm0 3h-2V9h2v2zm0 4H8v-2h8v2zM7 8H5V6h2v2zm0 3H5V9h2v2zm1-2h2v2H8V9zm0-3h2v2H8V6zm3 3h2v2h-2V9zm0-3h2v2h-2V6zm9-3H4c-1.1 0-1.99.9-1.99 2L2 15c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M12 23l4-4H8l4 4z"/>',keyboard_return:'<path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7z"/>',keyboard_tab:'<path d="M11.59 7.41L15.17 11H1v2h14.17l-3.59 3.59L13 18l6-6-6-6-1.41 1.41z"/><path d="M20 6v12h2V6h-2z"/>',keyboard_voice:'<path d="M12 15c1.66 0 2.99-1.34 2.99-3L15 6c0-1.66-1.34-3-3-3S9 4.34 9 6v6c0 1.66 1.34 3 3 3z"/><path d="M17.3 12c0 3-2.54 5.1-5.3 5.1S6.7 15 6.7 12H5c0 3.42 2.72 6.23 6 6.72V22h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>',laptop:'<path d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>',laptop_chromebook:'<path d="M22 18V3H2v15H0v2h24v-2h-2zm-8 0h-4v-1h4v1zm6-3H4V5h16v10z"/>',laptop_mac:'<path d="M20 18c1.1 0 1.99-.9 1.99-2L22 5c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2H0c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2h-4zM4 5h16v11H4V5zm8 14c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>',laptop_windows:'<path d="M20 18v-1c1.1 0 1.99-.9 1.99-2L22 5c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2v1H0v2h24v-2h-4zM4 5h16v10H4V5z"/>',memory:'<path d="M13 13h-2v-2h2v2zm2-4H9v6h6V9z"/><path d="M17 17H7V7h10v10zm4-6V9h-2V7c0-1.1-.9-2-2-2h-2V3h-2v2h-2V3H9v2H7c-1.1 0-2 .9-2 2v2H3v2h2v2H3v2h2v2c0 1.1.9 2 2 2h2v2h2v-2h2v2h2v-2h2c1.1 0 2-.9 2-2v-2h2v-2h-2v-2h2z"/>',mouse:'<path d="M13 1.07V9h7c0-4.08-3.05-7.44-7-7.93z"/><path d="M4 15c0 4.42 3.58 8 8 8s8-3.58 8-8v-4H4v4z"/><path d="M11 1.07C7.05 1.56 4 4.92 4 9h7V1.07z"/>',phone_android:'<path d="M16 1H8C6.34 1 5 2.34 5 4v16c0 1.66 1.34 3 3 3h8c1.66 0 3-1.34 3-3V4c0-1.66-1.34-3-3-3zm-2 20h-4v-1h4v1zm3.25-3H6.75V4h10.5v14z"/>',phone_iphone:'<path d="M15.5 1h-8C6.12 1 5 2.12 5 3.5v17C5 21.88 6.12 23 7.5 23h8c1.38 0 2.5-1.12 2.5-2.5v-17C18 2.12 16.88 1 15.5 1zm-4 21c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5-4H7V4h9v14z"/>',phonelink:'<path d="M4 6h18V4H4c-1.1 0-2 .9-2 2v11H0v3h14v-3H4V6z"/><path d="M22 17h-4v-7h4v7zm1-9h-6c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h6c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1z"/>',phonelink_off:'<path d="M22 6V4H6.82l2 2H22z"/><path d="M4 6.27L14.73 17H4V6.27zM1.92 1.65L.65 2.92l1.82 1.82C2.18 5.08 2 5.52 2 6v11H0v3h17.73l2.35 2.35 1.27-1.27L3.89 3.62 1.92 1.65z"/><path d="M23 8h-6c-.55 0-1 .45-1 1v4.18l2 2V10h4v7h-2.18l3 3H23c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1z"/>',power_input:'<path d="M2 9v2h19V9H2z"/><path d="M2 15h5v-2H2v2z"/><path d="M9 15h5v-2H9v2z"/><path d="M16 15h5v-2h-5v2z"/>',router:'<path d="M20.2 5.9l.8-.8C19.6 3.7 17.8 3 16 3c-1.8 0-3.6.7-5 2.1l.8.8C13 4.8 14.5 4.2 16 4.2s3 .6 4.2 1.7z"/><path d="M19.3 6.7c-.9-.9-2.1-1.4-3.3-1.4-1.2 0-2.4.5-3.3 1.4l.8.8c.7-.7 1.6-1 2.5-1 .9 0 1.8.3 2.5 1l.8-.8z"/><path d="M15 18h-2v-2h2v2zm-3.5 0h-2v-2h2v2zM8 18H6v-2h2v2zm11-5h-2V9h-2v4H5c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2z"/>',scanner:'<path d="M19.8 10.7L4.2 5l-.7 1.9L17.6 12H5c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-5.5c0-.8-.5-1.6-1.2-1.8zM7 17H5v-2h2v2zm12 0H9v-2h10v2z"/>',security:'<path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>',sim_card:'<path d="M19.99 4c0-1.1-.89-2-1.99-2h-8L4 8v12c0 1.1.9 2 2 2h12.01c1.1 0 1.99-.9 1.99-2l-.01-16zM9 19H7v-2h2v2zm8 0h-2v-2h2v2zm-8-4H7v-4h2v4zm4 4h-2v-4h2v4zm0-6h-2v-2h2v2zm4 2h-2v-4h2v4z"/>',smartphone:'<path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/>',speaker:'<path d="M12 20c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-16c1.1 0 2 .9 2 2s-.9 2-2 2c-1.11 0-2-.9-2-2s.89-2 2-2zm5-2H7c-1.1 0-2 .9-2 2v16c0 1.1.9 1.99 2 1.99L17 22c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/><path d="M12 12c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>',speaker_group:'<path d="M18.2 1H9.8C8.81 1 8 1.81 8 2.8v14.4c0 .99.81 1.79 1.8 1.79l8.4.01c.99 0 1.8-.81 1.8-1.8V2.8c0-.99-.81-1.8-1.8-1.8zM14 3c1.1 0 2 .89 2 2s-.9 2-2 2-2-.89-2-2 .9-2 2-2zm0 13.5c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/> <circle cx="14" cy="12.5" r="2.5"/> <path d="M6 5H4v16c0 1.1.89 2 2 2h10v-2H6V5z"/>',tablet:'<path d="M21 4H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h18c1.1 0 1.99-.9 1.99-2L23 6c0-1.1-.9-2-2-2zm-2 14H5V6h14v12z"/>',tablet_android:'<path d="M18 0H6C4.34 0 3 1.34 3 3v18c0 1.66 1.34 3 3 3h12c1.66 0 3-1.34 3-3V3c0-1.66-1.34-3-3-3zm-4 22h-4v-1h4v1zm5.25-3H4.75V3h14.5v16z"/>',tablet_mac:'<path d="M18.5 0h-14C3.12 0 2 1.12 2 2.5v19C2 22.88 3.12 24 4.5 24h14c1.38 0 2.5-1.12 2.5-2.5v-19C21 1.12 19.88 0 18.5 0zm-7 23c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm7.5-4H4V3h15v16z"/>',toys:'<path d="M12 12c0-3 2.5-5.5 5.5-5.5S23 9 23 12H12z"/><path d="M12 12c0 3-2.5 5.5-5.5 5.5S1 15 1 12h11z"/><path d="M12 12c-3 0-5.5-2.5-5.5-5.5S9 1 12 1v11z"/><path d="M12 12c3 0 5.5 2.5 5.5 5.5S15 23 12 23V12z"/>',tv:'<path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z"/>',vidiogame_asset:'<path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5S18.67 9 19.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>',watch:'<path d="M20 12c0-2.54-1.19-4.81-3.04-6.27L16 0H8l-.95 5.73C5.19 7.19 4 9.45 4 12s1.19 4.81 3.05 6.27L8 24h8l.96-5.73C18.81 16.81 20 14.54 20 12zM6 12c0-3.31 2.69-6 6-6s6 2.69 6 6-2.69 6-6 6-6-2.69-6-6z"/>',add_a_photo:'<path d="M3 4V1h2v3h3v2H5v3H3V6H0V4h3z"/><path d="M13 19c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-7-9V7h3V4h7l1.83 2H21c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V10h3z"/><path d="M9.8 14c0 1.77 1.43 3.2 3.2 3.2 1.77 0 3.2-1.43 3.2-3.2 0-1.77-1.43-3.2-3.2-3.2-1.77 0-3.2 1.43-3.2 3.2z"/>',add_to_photos:'<path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6z"/><path d="M19 11h-4v4h-2v-4H9V9h4V5h2v4h4v2zm1-9H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>',adjust:'<path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2z"/><path d="M15 12c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3z"/>',assistant_photo:'<path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>',audiotrack:'<path d="M12 3v9.28c-.47-.17-.97-.28-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z"/>',blur_circular:'<path d="M10 9c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M10 13c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M7 9.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M10 16.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M7 13.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M10 7.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M14 9c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M14 7.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M17 13.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M17 9.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M14 16.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M14 13c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/>',blur_linear:'<path d="M5 17.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5z"/><path d="M9 13c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M9 9c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M3 21h18v-2H3v2z"/><path d="M5 9.5c.83 0 1.5-.67 1.5-1.5S5.83 6.5 5 6.5 3.5 7.17 3.5 8 4.17 9.5 5 9.5z"/><path d="M5 13.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5z"/><path d="M9 17c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M17 16.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M3 3v2h18V3H3z"/><path d="M17 8.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M17 12.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M13 9c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M13 13c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M13 17c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/>',blur_off:'<path d="M14 7c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M13.8 11.48l.2.02c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5l.02.2c.09.67.61 1.19 1.28 1.28z"/><path d="M14 3.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M10 3.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M21 10.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M10 7c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M18 15c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M18 11c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M18 7c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M14 20.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M2.5 5.27l3.78 3.78L6 9c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1c0-.1-.03-.19-.06-.28l2.81 2.81c-.71.11-1.25.73-1.25 1.47 0 .83.67 1.5 1.5 1.5.74 0 1.36-.54 1.47-1.25l2.81 2.81c-.09-.03-.18-.06-.28-.06-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1c0-.1-.03-.19-.06-.28l3.78 3.78L20 20.23 3.77 4 2.5 5.27z"/><path d="M10 17c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M21 13.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M6 13c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M3 9.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M10 20.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M6 17c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M3 13.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/>',blur_on:'<path d="M6 13c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M6 17c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M6 9c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M3 9.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M6 5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M21 10.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M14 7c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M14 3.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M3 13.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M10 20.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M10 3.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5z"/><path d="M10 7c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/><path d="M10 12.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/><path d="M18 13c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M18 17c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M18 9c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M18 5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M21 13.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M14 17c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M14 20.5c-.28 0-.5.22-.5.5s.22.5.5.5.5-.22.5-.5-.22-.5-.5-.5z"/><path d="M10 8.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/><path d="M10 17c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M14 12.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/><path d="M14 8.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/>',brightness_1:'<circle cx="12" cy="12" r="10"/>',brightness_2:'<path d="M10 2c-1.82 0-3.53.5-5 1.35C7.99 5.08 10 8.3 10 12s-2.01 6.92-5 8.65C6.47 21.5 8.18 22 10 22c5.52 0 10-4.48 10-10S15.52 2 10 2z"/>',brightness_3:'<path d="M9 2c-1.05 0-2.05.16-3 .46 4.06 1.27 7 5.06 7 9.54 0 4.48-2.94 8.27-7 9.54.95.3 1.95.46 3 .46 5.52 0 10-4.48 10-10S14.52 2 9 2z"/>',brightness_4:'<path d="M20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69L23.31 12 20 8.69zM12 18c-.89 0-1.74-.2-2.5-.55C11.56 16.5 13 14.42 13 12s-1.44-4.5-3.5-5.45C10.26 6.2 11.11 6 12 6c3.31 0 6 2.69 6 6s-2.69 6-6 6z"/>',brightness_5:'<path d="M20 15.31L23.31 12 20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69zM12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/>',brightness_6:'<path d="M20 15.31L23.31 12 20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69zM12 18V6c3.31 0 6 2.69 6 6s-2.69 6-6 6z"/>',brightness_7:'<path d="M12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm8-9.31V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69L23.31 12 20 8.69z"/><path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"/>',broken_image:'<path d="M21 5v6.59l-3-3.01-4 4.01-4-4-4 4-3-3.01V5c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2z"/><path d="M18 11.42l3 3.01V19c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2v-6.58l3 2.99 4-4 4 4 4-3.99z"/>',brush:'<path d="M7 14c-1.66 0-3 1.34-3 3 0 1.31-1.16 2-2 2 .92 1.22 2.49 2 4 2 2.21 0 4-1.79 4-4 0-1.66-1.34-3-3-3z"/><path d="M20.71 4.63l-1.34-1.34c-.39-.39-1.02-.39-1.41 0L9 12.25 11.75 15l8.96-8.96c.39-.39.39-1.02 0-1.41z"/>',burst_mode:'<path d="M1 5h2v14H1z"/><path d="M5 5h2v14H5z"/><path d="M11 17l2.5-3.15L15.29 16l2.5-3.22L21 17H11zM22 5H10c-.55 0-1 .45-1 1v12c0 .55.45 1 1 1h12c.55 0 1-.45 1-1V6c0-.55-.45-1-1-1z"/>',camera:'<path d="M9.4 10.5l4.77-8.26C13.47 2.09 12.75 2 12 2c-2.4 0-4.6.85-6.32 2.25l3.66 6.35.06-.1z"/><path d="M21.54 9c-.92-2.92-3.15-5.26-6-6.34L11.88 9h9.66z"/><path d="M21.8 10h-7.49l.29.5 4.76 8.25C21 16.97 22 14.61 22 12c0-.69-.07-1.35-.2-2z"/><path d="M8.54 12l-3.9-6.75C3.01 7.03 2 9.39 2 12c0 .69.07 1.35.2 2h7.49l-1.15-2z"/><path d="M2.46 15c.92 2.92 3.15 5.26 6 6.34L12.12 15H2.46z"/><path d="M13.73 15l-3.9 6.76c.7.15 1.42.24 2.17.24 2.4 0 4.6-.85 6.32-2.25l-3.66-6.35-.93 1.6z"/>',camera_alt:'<circle cx="12" cy="12" r="3.2"/><path d="M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z"/>',camera_front:'<path d="M10 20H5v2h5v2l3-3-3-3v2z"/><path d="M14 20v2h5v-2h-5z"/><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-1.99.9-1.99 2S10.9 8 12 8z"/><path d="M7 2h10v10.5c0-1.67-3.33-2.5-5-2.5s-5 .83-5 2.5V2zm10-2H7C5.9 0 5 .9 5 2v14c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2z"/>',camera_rear:'<path d="M10 20H5v2h5v2l3-3-3-3v2z"/><path d="M14 20v2h5v-2h-5z"/><path d="M12 6c-1.11 0-2-.9-2-2s.89-2 1.99-2 2 .9 2 2C14 5.1 13.1 6 12 6zm5-6H7C5.9 0 5 .9 5 2v14c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2z"/>',camera_roll:'<path d="M14 5c0-1.1-.9-2-2-2h-1V2c0-.55-.45-1-1-1H6c-.55 0-1 .45-1 1v1H4c-1.1 0-2 .9-2 2v15c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2h8V5h-8zm-2 13h-2v-2h2v2zm0-9h-2V7h2v2zm4 9h-2v-2h2v2zm0-9h-2V7h2v2zm4 9h-2v-2h2v2zm0-9h-2V7h2v2z"/>',
center_focus_strong:'<path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"/><path d="M5 15H3v4c0 1.1.9 2 2 2h4v-2H5v-4z"/><path d="M5 5h4V3H5c-1.1 0-2 .9-2 2v4h2V5z"/><path d="M19 3h-4v2h4v4h2V5c0-1.1-.9-2-2-2z"/><path d="M19 19h-4v2h4c1.1 0 2-.9 2-2v-4h-2v4z"/>',center_focus_weak:'<path d="M5 15H3v4c0 1.1.9 2 2 2h4v-2H5v-4z"/><path d="M5 5h4V3H5c-1.1 0-2 .9-2 2v4h2V5z"/><path d="M19 3h-4v2h4v4h2V5c0-1.1-.9-2-2-2z"/><path d="M19 19h-4v2h4c1.1 0 2-.9 2-2v-4h-2v4z"/><path d="M12 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0-6c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"/>',collections:'<path d="M11 12l2.03 2.71L16 11l4 5H8l3-4zm11 4V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2z"/><path d="M2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z"/>',collections_bookmark:'<path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 10l-2.5-1.5L15 12V4h5v8z"/>',color_lens:'<path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>',colorize:'<path d="M20.71 5.63l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-3.12 3.12-1.93-1.91-1.41 1.41 1.42 1.42L3 16.25V21h4.75l8.92-8.92 1.42 1.42 1.41-1.41-1.92-1.92 3.12-3.12c.4-.4.4-1.03.01-1.42zM6.92 19L5 17.08l8.06-8.06 1.92 1.92L6.92 19z"/>',compare:'<path d="M10 18H5l5-6v6zm0-15H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h5v2h2V1h-2v2z"/><path d="M19 3h-5v2h5v13l-5-6v9h5c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',control_point:'<path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7z"/><path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2z"/>',control_point_duplicate:'<path d="M16 8h-2v3h-3v2h3v3h2v-3h3v-2h-3z"/><path d="M2 12c0-2.79 1.64-5.2 4.01-6.32V3.52C2.52 4.76 0 8.09 0 12s2.52 7.24 6.01 8.48v-2.16C3.64 17.2 2 14.79 2 12z"/><path d="M15 19c-3.86 0-7-3.14-7-7s3.14-7 7-7 7 3.14 7 7-3.14 7-7 7zm0-16c-4.96 0-9 4.04-9 9s4.04 9 9 9 9-4.04 9-9-4.04-9-9-9z"/>',crop:'<path d="M17 15h2V7c0-1.1-.9-2-2-2H9v2h8v8z"/><path d="M7 17V1H5v4H1v2h4v10c0 1.1.9 2 2 2h10v4h2v-4h4v-2H7z"/>',crop_16_9:'<path d="M19 6H5c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H5V8h14v8z"/>',crop_3_2:'<path d="M19 4H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H5V6h14v12z"/>',crop_5_4:'<path d="M19 5H5c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 12H5V7h14v10z"/>',crop_7_5:'<path d="M19 7H5c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm0 8H5V9h14v6z"/>',crop_din:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/>',crop_free:'<path d="M3 5v4h2V5h4V3H5c-1.1 0-2 .9-2 2z"/><path d="M5 15H3v4c0 1.1.9 2 2 2h4v-2H5v-4z"/><path d="M19 19h-4v2h4c1.1 0 2-.9 2-2v-4h-2v4z"/><path d="M19 3h-4v2h4v4h2V5c0-1.1-.9-2-2-2z"/>',crop_landscape:'<path d="M19 5H5c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 12H5V7h14v10z"/>',crop_original:'<path d="M19 19H5V5h14v14zm0-16H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M13.96 12.29l-2.75 3.54-1.96-2.36L6.5 17h11l-3.54-4.71z"/>',crop_portrait:'<path d="M17 3H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H7V5h10v14z"/>',crop_rotate:'<path d="M7.47 21.49C4.2 19.93 1.86 16.76 1.5 13H0c.51 6.16 5.66 11 11.95 11 .23 0 .44-.02.66-.03L8.8 20.15l-1.33 1.34z"/><path d="M12.05 0c-.23 0-.44.02-.66.04l3.81 3.81 1.33-1.33C19.8 4.07 22.14 7.24 22.5 11H24c-.51-6.16-5.66-11-11.95-11z"/><path d="M16 14h2V8a2 2 0 0 0-2-2h-6v2h6v6z"/><path d="M8 16V4H6v2H4v2h2v8a2 2 0 0 0 2 2h8v2h2v-2h2v-2H8z"/>',crop_square:'<path d="M18 4H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H6V6h12v12z"/>',dehaze:'<path d="M2 15.5v2h20v-2H2z"/><path d="M2 10.5v2h20v-2H2z"/><path d="M2 5.5v2h20v-2H2z"/>',details:'<path d="M3 4l9 16 9-16H3zm3.38 2h11.25L12 16 6.38 6z"/>',edit:'<path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"/><path d="M20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>',exposure:'<path d="M15 17v2h2v-2h2v-2h-2v-2h-2v2h-2v2h2z"/><path d="M20 20H4L20 4v16zM5 5h6v2H5V5zm15-3H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>',exposure_neg_1:'<path d="M4 11v2h8v-2H4z"/><path d="M19 18h-2V7.38L14 8.4V6.7L18.7 5h.3v13z"/>',exposure_neg_2:'<path d="M15.05 16.29l2.86-3.07c.38-.39.72-.79 1.04-1.18.32-.39.59-.78.82-1.17.23-.39.41-.78.54-1.17.13-.39.19-.79.19-1.18 0-.53-.09-1.02-.27-1.46-.18-.44-.44-.81-.78-1.11-.34-.31-.77-.54-1.26-.71A5.72 5.72 0 0 0 16.47 5c-.69 0-1.31.11-1.85.32-.54.21-1 .51-1.36.88-.37.37-.65.8-.84 1.3-.18.47-.27.97-.28 1.5h2.14c.01-.31.05-.6.13-.87.09-.29.23-.54.4-.75.18-.21.41-.37.68-.49.27-.12.6-.18.96-.18.31 0 .58.05.81.15.23.1.43.25.59.43.16.18.28.4.37.65.08.25.13.52.13.81 0 .22-.03.43-.08.65-.06.22-.15.45-.29.7-.14.25-.32.53-.56.83-.23.3-.52.65-.88 1.03l-4.17 4.55V18H21v-1.71h-5.95z"/><path d="M2 11v2h8v-2H2z"/>',exposure_plus_1:'<path d="M10 7H8v4H4v2h4v4h2v-4h4v-2h-4V7z"/><path d="M20 18h-2V7.38L15 8.4V6.7L19.7 5h.3v13z"/>',exposure_plus_2:'<path d="M16.05 16.29l2.86-3.07c.38-.39.72-.79 1.04-1.18.32-.39.59-.78.82-1.17.23-.39.41-.78.54-1.17.13-.39.19-.79.19-1.18 0-.53-.09-1.02-.27-1.46-.18-.44-.44-.81-.78-1.11-.34-.31-.77-.54-1.26-.71A5.72 5.72 0 0 0 17.47 5c-.69 0-1.31.11-1.85.32-.54.21-1 .51-1.36.88-.37.37-.65.8-.84 1.3-.18.47-.27.97-.28 1.5h2.14c.01-.31.05-.6.13-.87.09-.29.23-.54.4-.75.18-.21.41-.37.68-.49.27-.12.6-.18.96-.18.31 0 .58.05.81.15.23.1.43.25.59.43.16.18.28.4.37.65.08.25.13.52.13.81 0 .22-.03.43-.08.65-.06.22-.15.45-.29.7-.14.25-.32.53-.56.83-.23.3-.52.65-.88 1.03l-4.17 4.55V18H22v-1.71h-5.95z"/><path d="M8 7H6v4H2v2h4v4h2v-4h4v-2H8V7z"/>',exposure_zero:'<path d="M16.14 12.5c0 1-.1 1.85-.3 2.55-.2.7-.48 1.27-.83 1.7-.36.44-.79.75-1.3.95-.51.2-1.07.3-1.7.3-.62 0-1.18-.1-1.69-.3-.51-.2-.95-.51-1.31-.95-.36-.44-.65-1.01-.85-1.7-.2-.7-.3-1.55-.3-2.55v-2.04c0-1 .1-1.85.3-2.55.2-.7.48-1.26.84-1.69.36-.43.8-.74 1.31-.93C10.81 5.1 11.38 5 12 5c.63 0 1.19.1 1.7.29.51.19.95.5 1.31.93.36.43.64.99.84 1.69.2.7.3 1.54.3 2.55v2.04zm-2.11-2.36c0-.64-.05-1.18-.13-1.62-.09-.44-.22-.79-.4-1.06-.17-.27-.39-.46-.64-.58-.25-.13-.54-.19-.86-.19-.32 0-.61.06-.86.18s-.47.31-.64.58c-.17.27-.31.62-.4 1.06s-.13.98-.13 1.62v2.67c0 .64.05 1.18.14 1.62.09.45.23.81.4 1.09s.39.48.64.61.54.19.87.19c.33 0 .62-.06.87-.19s.46-.33.63-.61c.17-.28.3-.64.39-1.09.09-.45.13-.99.13-1.62v-2.66z"/>',filter:'<path d="M15.96 10.29l-2.75 3.54-1.96-2.36L8.5 15h11l-3.54-4.71z"/><path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/>',filter_1:'<path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M14 15h2V5h-4v2h2v8z"/><path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/>',filter_2:'<path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/><path d="M17 13h-4v-2h2a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4v2h4v2h-2a2 2 0 0 0-2 2v4h6v-2z"/>',filter_3:'<path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/><path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M17 13v-1.5c0-.83-.67-1.5-1.5-1.5.83 0 1.5-.67 1.5-1.5V7a2 2 0 0 0-2-2h-4v2h4v2h-2v2h2v2h-4v2h4a2 2 0 0 0 2-2z"/>',filter_4:'<path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M15 15h2V5h-2v4h-2V5h-2v6h4v4z"/><path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/>',filter_5:'<path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/><path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M17 13v-2a2 2 0 0 0-2-2h-2V7h4V5h-6v6h4v2h-4v2h4a2 2 0 0 0 2-2z"/>',filter_6:'<path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/><path d="M13 11h2v2h-2v-2zm0 4h2a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2h-2V7h4V5h-4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2z"/>',filter_7:'<path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/><path d="M13 15l4-8V5h-6v2h4l-4 8h2z"/>',filter_8:'<path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/><path d="M13 11h2v2h-2v-2zm0-4h2v2h-2V7zm0 8h2a2 2 0 0 0 2-2v-1.5c0-.83-.67-1.5-1.5-1.5.83 0 1.5-.67 1.5-1.5V7a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v1.5c0 .83.67 1.5 1.5 1.5-.83 0-1.5.67-1.5 1.5V13a2 2 0 0 0 2 2z"/>',filter_9:'<path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/><path d="M15 9h-2V7h2v2zm0-4h-2a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2v2h-4v2h4a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z"/>',filter_9_plus:'<path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M11 9V8h1v1h-1zm3 3V8a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2h1v1H9v2h3a2 2 0 0 0 2-2z"/><path d="M21 9h-2V7h-2v2h-2v2h2v2h2v-2h2v6H7V3h14v6zm0-8H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/>',filter_b_and_w:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16l-7-8v8H5l7-8V5h7v14z"/>',filter_center_focus:'<path d="M5 15H3v4c0 1.1.9 2 2 2h4v-2H5v-4z"/><path d="M5 5h4V3H5c-1.1 0-2 .9-2 2v4h2V5z"/><path d="M19 3h-4v2h4v4h2V5c0-1.1-.9-2-2-2z"/><path d="M19 19h-4v2h4c1.1 0 2-.9 2-2v-4h-2v4z"/><path d="M12 9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>',filter_drama:'<path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.61 5.64 5.36 8.04 2.35 8.36 0 10.9 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM19 18H6c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4h2c0-2.76-1.86-5.08-4.4-5.78C8.61 6.88 10.2 6 12 6c3.03 0 5.5 2.47 5.5 5.5v.5H19c1.65 0 3 1.35 3 3s-1.35 3-3 3z"/>',filter_frames:'<path d="M20 20H4V6h4.52l3.52-3.5L15.52 6H20v14zm0-16h-4l-4-4-4 4H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z"/><path d="M18 8H6v10h12"/>',filter_hdr:'<path d="M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z"/>',filter_none:'<path d="M3 5H1v16c0 1.1.9 2 2 2h16v-2H3V5z"/><path d="M21 17H7V3h14v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/>',filter_tilt_shift:'<path d="M11 4.07V2.05c-2.01.2-3.84 1-5.32 2.21L7.1 5.69A7.941 7.941 0 0 1 11 4.07z"/><path d="M18.32 4.26A9.949 9.949 0 0 0 13 2.05v2.02c1.46.18 2.79.76 3.9 1.62l1.42-1.43z"/><path d="M19.93 11h2.02c-.2-2.01-1-3.84-2.21-5.32L18.31 7.1a7.941 7.941 0 0 1 1.62 3.9z"/><path d="M5.69 7.1L4.26 5.68A9.949 9.949 0 0 0 2.05 11h2.02c.18-1.46.76-2.79 1.62-3.9z"/><path d="M4.07 13H2.05c.2 2.01 1 3.84 2.21 5.32l1.43-1.43A7.868 7.868 0 0 1 4.07 13z"/><path d="M15 12c0-1.66-1.34-3-3-3s-3 1.34-3 3 1.34 3 3 3 3-1.34 3-3z"/><path d="M18.31 16.9l1.43 1.43a9.981 9.981 0 0 0 2.21-5.32h-2.02a7.945 7.945 0 0 1-1.62 3.89z"/><path d="M13 19.93v2.02c2.01-.2 3.84-1 5.32-2.21l-1.43-1.43c-1.1.86-2.43 1.44-3.89 1.62z"/><path d="M5.68 19.74A9.981 9.981 0 0 0 11 21.95v-2.02a7.941 7.941 0 0 1-3.9-1.62l-1.42 1.43z"/>',filter_vintage:'<path d="M18.7 12.4c-.28-.16-.57-.29-.86-.4.29-.11.58-.24.86-.4 1.92-1.11 2.99-3.12 3-5.19-1.79-1.03-4.07-1.11-6 0-.28.16-.54.35-.78.54.05-.31.08-.63.08-.95 0-2.22-1.21-4.15-3-5.19C10.21 1.85 9 3.78 9 6c0 .32.03.64.08.95-.24-.2-.5-.39-.78-.55-1.92-1.11-4.2-1.03-6 0 0 2.07 1.07 4.08 3 5.19.28.16.57.29.86.4-.29.11-.58.24-.86.4-1.92 1.11-2.99 3.12-3 5.19 1.79 1.03 4.07 1.11 6 0 .28-.16.54-.35.78-.54-.05.32-.08.64-.08.96 0 2.22 1.21 4.15 3 5.19 1.79-1.04 3-2.97 3-5.19 0-.32-.03-.64-.08-.95.24.2.5.38.78.54 1.92 1.11 4.2 1.03 6 0-.01-2.07-1.08-4.08-3-5.19zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>',flare:'<path d="M7 11H1v2h6v-2z"/><path d="M9.17 7.76L7.05 5.64 5.64 7.05l2.12 2.12 1.41-1.41z"/><path d="M13 1h-2v6h2V1z"/><path d="M18.36 7.05l-1.41-1.41-2.12 2.12 1.41 1.41 2.12-2.12z"/><path d="M17 11v2h6v-2h-6z"/><path d="M12 9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/><path d="M14.83 16.24l2.12 2.12 1.41-1.41-2.12-2.12-1.41 1.41z"/><path d="M5.64 16.95l1.41 1.41 2.12-2.12-1.41-1.41-2.12 2.12z"/><path d="M11 23h2v-6h-2v6z"/>',flash_auto:'<path d="M3 2v12h3v9l7-12H9l4-9H3z"/><path d="M16.85 7.65L18 4l1.15 3.65h-2.3zM19 2h-2l-3.2 9h1.9l.7-2h3.2l.7 2h1.9L19 2z"/>',flash_off:'<path d="M3.27 3L2 4.27l5 5V13h3v9l3.58-6.14L17.73 20 19 18.73 3.27 3z"/><path d="M17 10h-4l4-8H7v2.18l8.46 8.46L17 10z"/>',flash_on:'<path d="M7 2v11h3v9l7-12h-4l4-8z"/>',flip:'<path d="M15 21h2v-2h-2v2z"/><path d="M19 9h2V7h-2v2z"/><path d="M3 5v14c0 1.1.9 2 2 2h4v-2H5V5h4V3H5c-1.1 0-2 .9-2 2z"/><path d="M19 3v2h2c0-1.1-.9-2-2-2z"/><path d="M11 23h2V1h-2v22z"/><path d="M19 17h2v-2h-2v2z"/><path d="M15 5h2V3h-2v2z"/><path d="M19 13h2v-2h-2v2z"/><path d="M19 21c1.1 0 2-.9 2-2h-2v2z"/>',gradient:'<path d="M11 9h2v2h-2zm-2 2h2v2H9zm4 0h2v2h-2zm2-2h2v2h-2zM7 9h2v2H7zm12-6H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 18H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm2-7h-2v2h2v2h-2v-2h-2v2h-2v-2h-2v2H9v-2H7v2H5v-2h2v-2H5V5h14v6z"/>',grain:'<path d="M10 12c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M6 8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M6 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M18 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/><path d="M14 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M18 12c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M14 8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M10 4c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>',grid_off:'<path d="M16 4h4v4h-4V4zM8 4v1.45l2 2V4h4v4h-3.45l2 2H14v1.45l2 2V10h4v4h-3.45l2 2H20v1.45l2 2V4c0-1.1-.9-2-2-2H4.55l2 2H8z"/><path d="M16 20v-1.46L17.46 20H16zm-2 0h-4v-4h3.45l.55.54V20zm-6-6H4v-4h3.45l.55.55V14zm0 6H4v-4h4v4zM4 6.55L5.45 8H4V6.55zm6 6L11.45 14H10v-1.45zM1.27 1.27L0 2.55l2 2V20c0 1.1.9 2 2 2h15.46l2 2 1.27-1.27L1.27 1.27z"/>',grid_on:'<path d="M20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM8 20H4v-4h4v4zm0-6H4v-4h4v4zm0-6H4V4h4v4zm6 12h-4v-4h4v4zm0-6h-4v-4h4v4zm0-6h-4V4h4v4zm6 12h-4v-4h4v4zm0-6h-4v-4h4v4zm0-6h-4V4h4v4z"/>',hdr_off:'<path d="M13 15h-2v-2.45l2 2V15zm5 2L3.27 2.27 2 3.55l4 4V11H4V7H2v10h2v-4h2v4h2V9.55l1 1V17h4c.67 0 1.26-.33 1.62-.84l6.34 6.34 1.27-1.27L18 17z"/><path d="M18 9h2v2h-2V9zm0 4h1l.82 3.27.73.73H22l-1.19-4.17c.7-.31 1.19-1.01 1.19-1.83V9c0-1.1-.9-2-2-2h-4v5.45l2 2V13z"/><path d="M15 11.45V9c0-1.1-.9-2-2-2h-2.45L15 11.45z"/>',hdr_on:'<path d="M6 11H4V7H2v10h2v-4h2v4h2V7H6v4z"/><path d="M13 15h-2V9h2v6zm0-8H9v10h4c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2z"/><path d="M20 11h-2V9h2v2zm2 0V9c0-1.1-.9-2-2-2h-4v10h2v-4h1l1 4h2l-1.19-4.17c.7-.31 1.19-1.01 1.19-1.83z"/>',hdr_strong:'<path d="M17 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6z"/><path d="M5 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0-6c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"/>',hdr_weak:'<path d="M5 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"/><path d="M17 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm0-10c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6z"/>',healing:'<path d="M17.73 12.02l3.98-3.98c.39-.39.39-1.02 0-1.41l-4.34-4.34c-.39-.39-1.02-.39-1.41 0l-3.98 3.98L8 2.29C7.8 2.1 7.55 2 7.29 2c-.25 0-.51.1-.7.29L2.25 6.63c-.39.39-.39 1.02 0 1.41l3.98 3.98L2.25 16c-.39.39-.39 1.02 0 1.41l4.34 4.34c.39.39 1.02.39 1.41 0l3.98-3.98 3.98 3.98c.2.2.45.29.71.29.26 0 .51-.1.71-.29l4.34-4.34c.39-.39.39-1.02 0-1.41l-3.99-3.98zM12 9c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-4.71 1.96L3.66 7.34l3.63-3.63 3.62 3.62-3.62 3.63zM10 13c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm2 2c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm2-4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2.66 9.34l-3.63-3.62 3.63-3.63 3.62 3.62-3.62 3.63z"/>',image:'<path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>',image_aspect_ratio:'<path d="M16 10h-2v2h2v-2z"/><path d="M16 14h-2v2h2v-2z"/><path d="M8 10H6v2h2v-2z"/><path d="M12 10h-2v2h2v-2z"/><path d="M20 18H4V6h16v12zm0-14H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z"/>',iso:'<path d="M19 19H5L19 5v14zM5.5 7.5h2v-2H9v2h2V9H9v2H7.5V9h-2V7.5zM19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M17 17v-1.5h-5V17h5z"/>',landscape:'<path d="M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z"/>',leak_add:'<path d="M6 3H3v3c1.66 0 3-1.34 3-3z"/><path d="M14 3h-2a9 9 0 0 1-9 9v2c6.08 0 11-4.93 11-11z"/><path d="M10 3H8c0 2.76-2.24 5-5 5v2c3.87 0 7-3.13 7-7z"/><path d="M10 21h2a9 9 0 0 1 9-9v-2c-6.07 0-11 4.93-11 11z"/><path d="M18 21h3v-3c-1.66 0-3 1.34-3 3z"/><path d="M14 21h2c0-2.76 2.24-5 5-5v-2c-3.87 0-7 3.13-7 7z"/>',leak_remove:'<path d="M10 3H8c0 .37-.04.72-.12 1.06l1.59 1.59C9.81 4.84 10 3.94 10 3z"/><path d="M3 4.27l2.84 2.84C5.03 7.67 4.06 8 3 8v2c1.61 0 3.09-.55 4.27-1.46L8.7 9.97A8.99 8.99 0 0 1 3 12v2c2.71 0 5.19-.99 7.11-2.62l2.5 2.5A11.044 11.044 0 0 0 10 21h2c0-2.16.76-4.14 2.03-5.69l1.43 1.43A6.922 6.922 0 0 0 14 21h2c0-1.06.33-2.03.89-2.84L19.73 21 21 19.73 4.27 3 3 4.27z"/><path d="M14 3h-2c0 1.5-.37 2.91-1.02 4.16l1.46 1.46C13.42 6.98 14 5.06 14 3z"/><path d="M19.94 16.12c.34-.08.69-.12 1.06-.12v-2c-.94 0-1.84.19-2.66.52l1.6 1.6z"/><path d="M15.38 11.56l1.46 1.46A8.98 8.98 0 0 1 21 12v-2c-2.06 0-3.98.58-5.62 1.56z"/>',lens:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>',linked_camera:'<circle cx="12" cy="14" r="3.2"/><path d="M16 3.33c2.58 0 4.67 2.09 4.67 4.67H22c0-3.31-2.69-6-6-6v1.33M16 6c1.11 0 2 .89 2 2h1.33c0-1.84-1.49-3.33-3.33-3.33V6"/><path d="M17 9c0-1.11-.89-2-2-2V4H9L7.17 6H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9h-5zm-5 10c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>',looks:'<path d="M12 10c-3.86 0-7 3.14-7 7h2c0-2.76 2.24-5 5-5s5 2.24 5 5h2c0-3.86-3.14-7-7-7z"/><path d="M12 6C5.93 6 1 10.93 1 17h2c0-4.96 4.04-9 9-9s9 4.04 9 9h2c0-6.07-4.93-11-11-11z"/>',looks_3:'<path d="M19.01 3h-14c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4 7.5c0 .83-.67 1.5-1.5 1.5.83 0 1.5.67 1.5 1.5V15c0 1.11-.9 2-2 2h-4v-2h4v-2h-2v-2h2V9h-4V7h4c1.1 0 2 .89 2 2v1.5z"/>',looks_4:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4 14h-2v-4H9V7h2v4h2V7h2v10z"/>',looks_5:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4 6h-4v2h2c1.1 0 2 .89 2 2v2c0 1.11-.9 2-2 2H9v-2h4v-2H9V7h6v2z"/>',looks_6:'<path d="M11 15h2v-2h-2v2zm8-12H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4 6h-4v2h2c1.1 0 2 .89 2 2v2c0 1.11-.9 2-2 2h-2c-1.1 0-2-.89-2-2V9c0-1.11.9-2 2-2h4v2z"/>',looks_one:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14h-2V9h-2V7h4v10z"/>',looks_two:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4 8c0 1.11-.9 2-2 2h-2v2h4v2H9v-4c0-1.11.9-2 2-2h2V9H9V7h4c1.1 0 2 .89 2 2v2z"/>',loupe:'<path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7z"/><path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.49 2 2 6.49 2 12s4.49 10 10 10h8c1.1 0 2-.9 2-2v-8c0-5.51-4.49-10-10-10z"/>',monochrome_photos:'<path d="M20 19h-8v-1c-2.8 0-5-2.2-5-5s2.2-5 5-5V7h8v12zm0-14h-3.2L15 3H9L7.2 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2z"/><path d="M17 13c0-2.8-2.2-5-5-5v1.8c1.8 0 3.2 1.4 3.2 3.2 0 1.8-1.4 3.2-3.2 3.2V18c2.8 0 5-2.2 5-5z"/><path d="M8.8 13c0 1.8 1.4 3.2 3.2 3.2V9.8c-1.8 0-3.2 1.4-3.2 3.2z"/>',movie_creation:'<path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>',movie_filter:'<path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4zm-6.58 11.68L10.37 18l-1.05-2.32L7 14.63l2.32-1.05 1.05-2.32 1.05 2.32 2.32 1.05-2.32 1.05zm3.69-3.47l-.53 1.16-.53-1.16-1.16-.53 1.16-.53.53-1.15.53 1.16 1.16.53-1.16.52z"/>',music_note:'<path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>',nature:'<path d="M13 16.12c3.47-.41 6.17-3.36 6.17-6.95 0-3.87-3.13-7-7-7s-7 3.13-7 7c0 3.47 2.52 6.34 5.83 6.89V20H5v2h14v-2h-6v-3.88z"/>',nature_people:'<path d="M22.17 9.17c0-3.87-3.13-7-7-7s-7 3.13-7 7c0 3.47 2.52 6.34 5.83 6.89V20H6v-3h1v-4c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v4h1v5h16v-2h-3v-3.88c3.47-.41 6.17-3.36 6.17-6.95zM4.5 11c.83 0 1.5-.67 1.5-1.5S5.33 8 4.5 8 3 8.67 3 9.5 3.67 11 4.5 11z"/>',navigate_before:'<path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>',navigate_next:'<path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>',palette:'<path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>',panorama:'<path d="M23 18V6c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2zM8.5 12.5l2.5 3.01L14.5 11l4.5 6H5l3.5-4.5z"/>',panorama_fisheye:'<path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>',panorama_horizontal:'<path d="M20 6.54v10.91c-2.6-.77-5.28-1.16-8-1.16-2.72 0-5.4.39-8 1.16V6.54c2.6.77 5.28 1.16 8 1.16 2.72.01 5.4-.38 8-1.16M21.43 4c-.1 0-.2.02-.31.06C18.18 5.16 15.09 5.7 12 5.7c-3.09 0-6.18-.55-9.12-1.64-.11-.04-.22-.06-.31-.06-.34 0-.57.23-.57.63v14.75c0 .39.23.62.57.62.1 0 .2-.02.31-.06 2.94-1.1 6.03-1.64 9.12-1.64 3.09 0 6.18.55 9.12 1.64.11.04.21.06.31.06.33 0 .57-.23.57-.63V4.63c0-.4-.24-.63-.57-.63z"/>',panorama_vertical:'<path d="M19.94 21.12c-1.1-2.94-1.64-6.03-1.64-9.12 0-3.09.55-6.18 1.64-9.12.04-.11.06-.22.06-.31 0-.34-.23-.57-.63-.57H4.63c-.4 0-.63.23-.63.57 0 .1.02.2.06.31C5.16 5.82 5.71 8.91 5.71 12c0 3.09-.55 6.18-1.64 9.12-.05.11-.07.22-.07.31 0 .33.23.57.63.57h14.75c.39 0 .63-.24.63-.57-.01-.1-.03-.2-.07-.31zM6.54 20c.77-2.6 1.16-5.28 1.16-8 0-2.72-.39-5.4-1.16-8h10.91c-.77 2.6-1.16 5.28-1.16 8 0 2.72.39 5.4 1.16 8H6.54z"/>',panorama_wide_angle:'<path d="M12 6c2.45 0 4.71.2 7.29.64.47 1.78.71 3.58.71 5.36 0 1.78-.24 3.58-.71 5.36-2.58.44-4.84.64-7.29.64s-4.71-.2-7.29-.64C4.24 15.58 4 13.78 4 12c0-1.78.24-3.58.71-5.36C7.29 6.2 9.55 6 12 6m0-2c-2.73 0-5.22.24-7.95.72l-.93.16-.25.9C2.29 7.85 2 9.93 2 12s.29 4.15.87 6.22l.25.89.93.16c2.73.49 5.22.73 7.95.73s5.22-.24 7.95-.72l.93-.16.25-.89c.58-2.08.87-4.16.87-6.23s-.29-4.15-.87-6.22l-.25-.89-.93-.16C17.22 4.24 14.73 4 12 4z"/>',photo:'<path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>',photo_album:'<path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4zm0 15l3-3.86 2.14 2.58 3-3.86L18 19H6z"/>',photo_camera:'<circle cx="12" cy="12" r="3.2"/><path d="M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z"/>',photo_filter:'<path d="M17.13 8.9l.59-1.3 1.3-.6-1.3-.59-.59-1.3-.59 1.3-1.31.59 1.31.6z"/><path d="M12.39 6.53l-1.18 2.61-2.61 1.18 2.61 1.18 1.18 2.61 1.19-2.61 2.6-1.18-2.6-1.18z"/><path d="M19.02 10v9H5V5h9V3H5.02c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-9h-2z"/>',photo_library:'<path d="M11 12l2.03 2.71L16 11l4 5H8l3-4zm11 4V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2z"/><path d="M2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z"/>',photo_size_select_actual:'<path d="M21 3H3C2 3 1 4 1 5v14c0 1.1.9 2 2 2h18c1 0 2-1 2-2V5c0-1-1-2-2-2zM5 17l3.5-4.5 2.5 3.01L14.5 11l4.5 6H5z"/>',photo_size_select_large:'<path d="M21 15h2v2h-2v-2z"/><path d="M21 11h2v2h-2v-2z"/><path d="M23 19h-2v2c1 0 2-1 2-2z"/><path d="M13 3h2v2h-2V3z"/><path d="M21 7h2v2h-2V7z"/><path d="M21 3v2h2c0-1-1-2-2-2z"/><path d="M1 7h2v2H1V7z"/><path d="M17 3h2v2h-2V3z"/><path d="M17 19h2v2h-2v-2z"/><path d="M3 3C2 3 1 4 1 5h2V3z"/><path d="M9 3h2v2H9V3z"/><path d="M5 3h2v2H5V3z"/><path d="M3 19l2.5-3.21 1.79 2.15 2.5-3.22L13 19H3zm-2-8v8c0 1.1.9 2 2 2h12V11H1z"/>',photo_size_select_small:'<path d="M23 15h-2v2h2v-2z"/><path d="M23 11h-2v2h2v-2z"/><path d="M23 19h-2v2c1 0 2-1 2-2z"/><path d="M15 3h-2v2h2V3z"/><path d="M23 7h-2v2h2V7z"/><path d="M21 3v2h2c0-1-1-2-2-2z"/><path d="M3 21h8v-6H1v4c0 1.1.9 2 2 2z"/><path d="M3 7H1v2h2V7z"/><path d="M15 19h-2v2h2v-2z"/><path d="M19 3h-2v2h2V3z"/><path d="M19 19h-2v2h2v-2z"/><path d="M3 3C2 3 1 4 1 5h2V3z"/><path d="M3 11H1v2h2v-2z"/><path d="M11 3H9v2h2V3z"/><path d="M7 3H5v2h2V3z"/>',picture_as_pdf:'<path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6z"/><path d="M14 11.5h1v-3h-1v3zm-5-2h1v-1H9v1zm11.5-1H19v1h1.5V11H19v2h-1.5V7h3v1.5zm-4 3c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm-5-2c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zM20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>',portrait:'<path d="M16.5 16.25c0-1.5-3-2.25-4.5-2.25s-4.5.75-4.5 2.25V17h9v-.75zm-4.5-4c1.24 0 2.25-1.01 2.25-2.25S13.24 7.75 12 7.75 9.75 8.76 9.75 10s1.01 2.25 2.25 2.25z"/><path d="M19 19H5V5h14v14zm0-16H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',remove_red_eye:'<path d="M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-12.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5z"/><path d="M12 9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>',rotate_90_degrees_ccw:'<path d="M3.69 12.9l3.66-3.66L11 12.9l-3.66 3.66-3.65-3.66zm3.65-6.49L.86 12.9l6.49 6.48 6.49-6.48-6.5-6.49z"/><path d="M19.36 6.64A8.95 8.95 0 0 0 13 4V.76L8.76 5 13 9.24V6c1.79 0 3.58.68 4.95 2.05a7.007 7.007 0 0 1 0 9.9 6.973 6.973 0 0 1-7.79 1.44l-1.49 1.49C10.02 21.62 11.51 22 13 22c2.3 0 4.61-.88 6.36-2.64a8.98 8.98 0 0 0 0-12.72z"/>',rotate_left:'<path d="M7.11 8.53L5.7 7.11C4.8 8.27 4.24 9.61 4.07 11h2.02c.14-.87.49-1.72 1.02-2.47z"/><path d="M6.09 13H4.07c.17 1.39.72 2.73 1.62 3.89l1.41-1.42c-.52-.75-.87-1.59-1.01-2.47z"/><path d="M7.1 18.32c1.16.9 2.51 1.44 3.9 1.61V17.9c-.87-.15-1.71-.49-2.46-1.03L7.1 18.32z"/><path d="M13 4.07V1L8.45 5.55 13 10V6.09c2.84.48 5 2.94 5 5.91s-2.16 5.43-5 5.91v2.02c3.95-.49 7-3.85 7-7.93s-3.05-7.44-7-7.93z"/>',rotate_right:'<path d="M15.55 5.55L11 1v3.07C7.06 4.56 4 7.92 4 12s3.05 7.44 7 7.93v-2.02c-2.84-.48-5-2.94-5-5.91s2.16-5.43 5-5.91V10l4.55-4.45z"/><path d="M19.93 11c-.17-1.39-.72-2.73-1.62-3.89l-1.42 1.42c.54.75.88 1.6 1.02 2.47h2.02z"/><path d="M13 17.9v2.02c1.39-.17 2.74-.71 3.9-1.61l-1.44-1.44c-.75.54-1.59.89-2.46 1.03z"/><path d="M16.89 15.48l1.42 1.41c.9-1.16 1.45-2.5 1.62-3.89h-2.02c-.14.87-.48 1.72-1.02 2.48z"/>',slideshow:'<path d="M10 8v8l5-4-5-4z"/><path d="M19 19H5V5h14v14zm0-16H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',straighten:'<path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h2v4h2V8h2v4h2V8h2v4h2V8h2v4h2V8h2v8z"/>',style:'<path d="M2.53 19.65l1.34.56v-9.03l-2.43 5.86c-.41 1.02.08 2.19 1.09 2.61z"/><path d="M7.88 8.75c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm14.15 7.2L17.07 3.98a2.013 2.013 0 0 0-1.81-1.23c-.26 0-.53.04-.79.15L7.1 5.95a1.999 1.999 0 0 0-1.08 2.6l4.96 11.97a1.998 1.998 0 0 0 2.6 1.08l7.36-3.05a1.994 1.994 0 0 0 1.09-2.6z"/><path d="M5.88 19.75c0 1.1.9 2 2 2h1.45l-3.45-8.34v6.34z"/>',switch_camera:'<path d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 11.5V13H9v2.5L5.5 12 9 8.5V11h6V8.5l3.5 3.5-3.5 3.5z"/>',switch_video:'<path d="M18 9.5V6c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v12c0 .55.45 1 1 1h14c.55 0 1-.45 1-1v-3.5l4 4v-13l-4 4zm-5 6V13H7v2.5L3.5 12 7 8.5V11h6V8.5l3.5 3.5-3.5 3.5z"/>',tag_faces:'<path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.01-18C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2z"/><path d="M15.5 11c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5z"/><path d="M8.5 11c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11z"/><path d="M12 17.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>',texture:'<path d="M19.51 3.08L3.08 19.51c.09.34.27.65.51.9.25.24.56.42.9.51L20.93 4.49c-.19-.69-.73-1.23-1.42-1.41z"/><path d="M11.88 3L3 11.88v2.83L14.71 3h-2.83z"/><path d="M5 3c-1.1 0-2 .9-2 2v2l4-4H5z"/><path d="M19 21c.55 0 1.05-.22 1.41-.59.37-.36.59-.86.59-1.41v-2l-4 4h2z"/><path d="M9.29 21h2.83L21 12.12V9.29L9.29 21z"/>',timelapse:'<path d="M16.24 7.76C15.07 6.59 13.54 6 12 6v6l-4.24 4.24c2.34 2.34 6.14 2.34 8.49 0 2.34-2.34 2.34-6.14-.01-8.48z"/><path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>',timer:'<path d="M15 1H9v2h6V1z"/><path d="M11 14h2V8h-2v6z"/><path d="M12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm7.03-12.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42C16.07 4.74 14.12 4 12 4c-4.97 0-9 4.03-9 9s4.02 9 9 9 9-4.03 9-9c0-2.12-.74-4.07-1.97-5.61z"/>',timer_10:'<path d="M0 7.72V9.4l3-1V18h2V6h-.25L0 7.72z"/><path d="M23.78 14.37c-.14-.28-.35-.53-.63-.74-.28-.21-.61-.39-1.01-.53s-.85-.27-1.35-.38a6.64 6.64 0 0 1-.87-.23 2.61 2.61 0 0 1-.55-.25.717.717 0 0 1-.28-.3.978.978 0 0 1 .01-.8c.06-.13.15-.25.27-.34.12-.1.27-.18.45-.24s.4-.09.64-.09c.25 0 .47.04.66.11.19.07.35.17.48.29.13.12.22.26.29.42.06.16.1.32.1.49h1.95a2.517 2.517 0 0 0-.93-1.97c-.3-.25-.66-.44-1.09-.59C21.49 9.07 21 9 20.46 9c-.51 0-.98.07-1.39.21-.41.14-.77.33-1.06.57-.29.24-.51.52-.67.84a2.2 2.2 0 0 0-.23 1.01c0 .36.08.69.23.96.15.28.36.52.64.73.27.21.6.38.98.53.38.14.81.26 1.27.36.39.08.71.17.95.26s.43.19.57.29c.13.1.22.22.27.34.05.12.07.25.07.39 0 .32-.13.57-.4.77-.27.2-.66.29-1.17.29-.22 0-.43-.02-.64-.08-.21-.05-.4-.13-.56-.24a1.333 1.333 0 0 1-.59-1.11h-1.89c0 .36.08.71.24 1.05.16.34.39.65.7.93.31.27.69.49 1.15.66.46.17.98.25 1.58.25.53 0 1.01-.06 1.44-.19.43-.13.8-.31 1.11-.54.31-.23.54-.51.71-.83.17-.32.25-.67.25-1.06-.02-.4-.09-.74-.24-1.02z"/><path d="M12.9 13.22c0 .6-.04 1.11-.12 1.53-.08.42-.2.76-.36 1.02-.16.26-.36.45-.59.57-.23.12-.51.18-.82.18-.3 0-.58-.06-.82-.18s-.44-.31-.6-.57c-.16-.26-.29-.6-.38-1.02-.09-.42-.13-.93-.13-1.53v-2.5c0-.6.04-1.11.13-1.52.09-.41.21-.74.38-1 .16-.25.36-.43.6-.55.24-.11.51-.17.81-.17.31 0 .58.06.81.17.24.11.44.29.6.55.16.25.29.58.37.99.08.41.13.92.13 1.52v2.51zm.92-6.17c-.34-.4-.75-.7-1.23-.88-.47-.18-1.01-.27-1.59-.27-.58 0-1.11.09-1.59.27-.48.18-.89.47-1.23.88-.34.41-.6.93-.79 1.59-.18.65-.28 1.45-.28 2.39v1.92c0 .94.09 1.74.28 2.39.19.66.45 1.19.8 1.6.34.41.75.71 1.23.89.48.18 1.01.28 1.59.28.59 0 1.12-.09 1.59-.28.48-.18.88-.48 1.22-.89.34-.41.6-.94.78-1.6.18-.65.28-1.45.28-2.39v-1.92c0-.94-.09-1.74-.28-2.39-.18-.66-.44-1.19-.78-1.59z"/>',timer_3:'<path d="M11.61 12.97c-.16-.24-.36-.46-.62-.65a3.38 3.38 0 0 0-.93-.48c.3-.14.57-.3.8-.5.23-.2.42-.41.57-.64.15-.23.27-.46.34-.71.08-.24.11-.49.11-.73 0-.55-.09-1.04-.28-1.46-.18-.42-.44-.77-.78-1.06-.33-.28-.73-.5-1.2-.64-.45-.13-.97-.2-1.53-.2-.55 0-1.06.08-1.52.24-.47.17-.87.4-1.2.69-.33.29-.6.63-.78 1.03-.2.39-.29.83-.29 1.29h1.98c0-.26.05-.49.14-.69.09-.2.22-.38.38-.52.17-.14.36-.25.58-.33.22-.08.46-.12.73-.12.61 0 1.06.16 1.36.47.3.31.44.75.44 1.32 0 .27-.04.52-.12.74-.08.22-.21.41-.38.57-.17.16-.38.28-.63.37-.25.09-.55.13-.89.13H6.72v1.57H7.9c.34 0 .64.04.91.11.27.08.5.19.69.35.19.16.34.36.44.61.1.24.16.54.16.87 0 .62-.18 1.09-.53 1.42-.35.33-.84.49-1.45.49-.29 0-.56-.04-.8-.13-.24-.08-.44-.2-.61-.36-.17-.16-.3-.34-.39-.56-.09-.22-.14-.46-.14-.72H4.19c0 .55.11 1.03.32 1.45.21.42.5.77.86 1.05s.77.49 1.24.63.96.21 1.48.21c.57 0 1.09-.08 1.58-.23.49-.15.91-.38 1.26-.68.36-.3.64-.66.84-1.1.2-.43.3-.93.3-1.48 0-.29-.04-.58-.11-.86-.08-.25-.19-.51-.35-.76z"/><path d="M20.87 14.37c-.14-.28-.35-.53-.63-.74-.28-.21-.61-.39-1.01-.53s-.85-.27-1.35-.38a6.64 6.64 0 0 1-.87-.23 2.61 2.61 0 0 1-.55-.25.717.717 0 0 1-.28-.3.935.935 0 0 1-.08-.39.946.946 0 0 1 .36-.75c.12-.1.27-.18.45-.24s.4-.09.64-.09c.25 0 .47.04.66.11.19.07.35.17.48.29.13.12.22.26.29.42.06.16.1.32.1.49h1.95a2.517 2.517 0 0 0-.93-1.97c-.3-.25-.66-.44-1.09-.59-.43-.15-.92-.22-1.46-.22-.51 0-.98.07-1.39.21-.41.14-.77.33-1.06.57-.29.24-.51.52-.67.84a2.2 2.2 0 0 0-.23 1.01c0 .36.08.68.23.96.15.28.37.52.64.73.27.21.6.38.98.53.38.14.81.26 1.27.36.39.08.71.17.95.26s.43.19.57.29c.13.1.22.22.27.34.05.12.07.25.07.39 0 .32-.13.57-.4.77-.27.2-.66.29-1.17.29-.22 0-.43-.02-.64-.08-.21-.05-.4-.13-.56-.24a1.333 1.333 0 0 1-.59-1.11h-1.89c0 .36.08.71.24 1.05.16.34.39.65.7.93.31.27.69.49 1.15.66.46.17.98.25 1.58.25.53 0 1.01-.06 1.44-.19.43-.13.8-.31 1.11-.54.31-.23.54-.51.71-.83.17-.32.25-.67.25-1.06-.02-.4-.09-.74-.24-1.02z"/>',
timer_off:'<path d="M19.04 4.55l-1.42 1.42a9.012 9.012 0 0 0-10.57-.49l1.46 1.46C9.53 6.35 10.73 6 12 6c3.87 0 7 3.13 7 7 0 1.27-.35 2.47-.94 3.49l1.45 1.45A8.878 8.878 0 0 0 21 13c0-2.12-.74-4.07-1.97-5.61l1.42-1.42-1.41-1.42z"/><path d="M15 1H9v2h6V1z"/><path d="M11 9.44l2 2V8h-2v1.44z"/><path d="M12 20c-3.87 0-7-3.13-7-7 0-1.28.35-2.48.95-3.52l9.56 9.56c-1.03.61-2.23.96-3.51.96zM3.02 4L1.75 5.27 4.5 8.03A8.905 8.905 0 0 0 3 13c0 4.97 4.02 9 9 9 1.84 0 3.55-.55 4.98-1.5l2.5 2.5 1.27-1.27-7.71-7.71L3.02 4z"/>',tonality:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93s3.05-7.44 7-7.93v15.86zm2-15.86c1.03.13 2 .45 2.87.93H13v-.93zM13 7h5.24c.25.31.48.65.68 1H13V7zm0 3h6.74c.08.33.15.66.19 1H13v-1zm0 9.93V19h2.87c-.87.48-1.84.8-2.87.93zM18.24 17H13v-1h5.92c-.2.35-.43.69-.68 1zm1.5-3H13v-1h6.93c-.04.34-.11.67-.19 1z"/>',transform:'<path d="M22 18v-2H8V4h2L7 1 4 4h2v2H2v2h4v8c0 1.1.9 2 2 2h8v2h-2l3 3 3-3h-2v-2h4z"/><path d="M10 8h6v6h2V8c0-1.1-.9-2-2-2h-6v2z"/>',tune:'<path d="M13 21v-2h8v-2h-8v-2h-2v6h2zM3 17v2h6v-2H3z"/><path d="M21 13v-2H11v2h10zM7 9v2H3v2h4v2h2V9H7z"/><path d="M15 9h2V7h4V5h-4V3h-2v6zM3 5v2h10V5H3z"/>',view_comfy:'<path d="M3 9h4V5H3v4z"/><path d="M3 14h4v-4H3v4z"/><path d="M8 14h4v-4H8v4z"/><path d="M13 14h4v-4h-4v4z"/><path d="M8 9h4V5H8v4z"/><path d="M13 5v4h4V5h-4z"/><path d="M18 14h4v-4h-4v4z"/><path d="M3 19h4v-4H3v4z"/><path d="M8 19h4v-4H8v4z"/><path d="M13 19h4v-4h-4v4z"/><path d="M18 19h4v-4h-4v4z"/><path d="M18 5v4h4V5h-4z"/>',view_compact:'<path d="M3 19h6v-7H3v7z"/><path d="M10 19h12v-7H10v7z"/><path d="M3 5v6h19V5H3z"/>',vignette:'<path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 15c-4.42 0-8-2.69-8-6s3.58-6 8-6 8 2.69 8 6-3.58 6-8 6z"/>',wb_auto:'<path d="M6.85 12.65h2.3L8 9l-1.15 3.65zM22 7l-1.2 6.29L19.3 7h-1.6l-1.49 6.29L15 7h-.76C12.77 5.17 10.53 4 8 4c-4.42 0-8 3.58-8 8s3.58 8 8 8c3.13 0 5.84-1.81 7.15-4.43l.1.43H17l1.5-6.1L20 16h1.75l2.05-9H22zm-11.7 9l-.7-2H6.4l-.7 2H3.8L7 7h2l3.2 9h-1.9z"/>',wb_cloudy:'<path d="M19.36 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.64-4.96z"/>',wb_incandescent:'<path d="M3.55 18.54l1.41 1.41 1.79-1.8-1.41-1.41-1.79 1.8z"/><path d="M11 22.45h2V19.5h-2v2.95z"/><path d="M4 10.5H1v2h3v-2z"/><path d="M15 6.31V1.5H9v4.81C7.21 7.35 6 9.28 6 11.5c0 3.31 2.69 6 6 6s6-2.69 6-6c0-2.22-1.21-4.15-3-5.19z"/><path d="M20 10.5v2h3v-2h-3z"/><path d="M17.24 18.16l1.79 1.8 1.41-1.41-1.8-1.79-1.4 1.4z"/>',wb_irradescent:'<path d="M5 14.5h14v-6H5v6z"/><path d="M11 .55V3.5h2V.55h-2z"/><path d="M19.04 3.05l-1.79 1.79 1.41 1.41 1.8-1.79-1.42-1.41z"/><path d="M13 22.45V19.5h-2v2.95h2z"/><path d="M20.45 18.54l-1.8-1.79-1.41 1.41 1.79 1.8 1.42-1.42z"/><path d="M3.55 4.46l1.79 1.79 1.41-1.41-1.79-1.79-1.41 1.41z"/><path d="M4.96 19.95l1.79-1.8-1.41-1.41-1.79 1.79 1.41 1.42z"/>',wb_sunny:'<path d="M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.79 1.42-1.41z"/><path d="M4 10.5H1v2h3v-2z"/><path d="M13 .55h-2V3.5h2V.55z"/><path d="M20.45 4.46l-1.41-1.41-1.79 1.79 1.41 1.41 1.79-1.79z"/><path d="M17.24 18.16l1.79 1.8 1.41-1.41-1.8-1.79-1.4 1.4z"/><path d="M20 10.5v2h3v-2h-3z"/><path d="M12 5.5c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6z"/><path d="M11 22.45h2V19.5h-2v2.95z"/><path d="M3.55 18.54l1.41 1.41 1.79-1.8-1.41-1.41-1.79 1.8z"/>',add_location:'<path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm4 8h-3v3h-2v-3H8V8h3V5h2v3h3v2z"/>',beenhere:'<path d="M19 1H5c-1.1 0-1.99.9-1.99 2L3 15.93c0 .69.35 1.3.88 1.66L12 23l8.11-5.41c.53-.36.88-.97.88-1.66L21 3c0-1.1-.9-2-2-2zm-9 15l-5-5 1.41-1.41L10 13.17l7.59-7.59L19 7l-9 9z"/>',directions:'<path d="M21.71 11.29l-9-9c-.39-.39-1.02-.39-1.41 0l-9 9c-.39.39-.39 1.02 0 1.41l9 9c.39.39 1.02.39 1.41 0l9-9c.39-.38.39-1.01 0-1.41zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z"/>',directions_bike:'<path d="M16 4.8c.99 0 1.8-.81 1.8-1.8s-.81-1.8-1.8-1.8c-1 0-1.8.81-1.8 1.8S15 4.8 16 4.8z"/><path d="M19 20.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5zm0-8.5c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z"/><path d="M14.8 10H19V8.2h-3.2l-1.93-3.27c-.3-.5-.84-.83-1.46-.83-.47 0-.89.19-1.2.5l-3.7 3.7c-.32.3-.51.73-.51 1.2 0 .63.33 1.16.85 1.47L11.2 13v5H13v-6.48l-2.25-1.67 2.32-2.33L14.8 10z"/><path d="M5 20.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5zM5 12c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z"/>',directions_bus:'<path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z"/>',directions_car:'<path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>',directions_ferry:'<path d="M20 21c-1.39 0-2.78-.47-4-1.32-2.44 1.71-5.56 1.71-8 0C6.78 20.53 5.39 21 4 21H2v2h2c1.38 0 2.74-.35 4-.99 2.52 1.29 5.48 1.29 8 0 1.26.65 2.62.99 4 .99h2v-2h-2z"/><path d="M6 6h12v3.97L12 8 6 9.97V6zM3.95 19H4c1.6 0 3.02-.88 4-2 .98 1.12 2.4 2 4 2s3.02-.88 4-2c.98 1.12 2.4 2 4 2h.05l1.89-6.68c.08-.26.06-.54-.06-.78s-.34-.42-.6-.5L20 10.62V6c0-1.1-.9-2-2-2h-3V1H9v3H6c-1.1 0-2 .9-2 2v4.62l-1.29.42c-.26.08-.48.26-.6.5s-.15.52-.06.78L3.95 19z"/>',directions_subway:'<path d="M12 2c-4.42 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h12v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-6H6V6h5v5zm5.5 6c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6h-5V6h5v5z"/>',directions_train:'<path d="M4 15.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h12v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V5c0-3.5-3.58-4-8-4s-8 .5-8 4v10.5zm8 1.5c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm6-7H6V5h12v5z"/>',directions_transit:'<path d="M12 2c-4.42 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h12v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-6H6V6h5v5zm5.5 6c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6h-5V6h5v5z"/>',directions_walk:'<path d="M14 3.8c.99 0 1.8-.81 1.8-1.8 0-1-.81-1.8-1.8-1.8-1 0-1.8.81-1.8 1.8S13 3.8 14 3.8z"/><path d="M14.12 10H19V8.2h-3.62l-2-3.33c-.3-.5-.84-.83-1.46-.83-.17 0-.34.03-.49.07L6 5.8V11h1.8V7.33l2.11-.66L6 22h1.8l2.87-8.11L13 17v5h1.8v-6.41l-2.49-4.54.73-2.87L14.12 10z"/>',edit_location:'<path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm-1.56 10H9v-1.44l3.35-3.34 1.43 1.43L10.44 12zm4.45-4.45l-.7.7-1.44-1.44.7-.7c.15-.15.39-.15.54 0l.9.9c.15.15.15.39 0 .54z"/>',ev_station:'<path d="M19.77 7.23l.01-.01-3.72-3.72L15 4.56l2.11 2.11c-.94.36-1.61 1.26-1.61 2.33 0 1.38 1.12 2.5 2.5 2.5.36 0 .69-.08 1-.21v7.21c0 .55-.45 1-1 1s-1-.45-1-1V14c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v16h10v-7.5h1.5v5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V9c0-.69-.28-1.32-.73-1.77zM18 10c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zM8 18v-4.5H6L10 6v5h2l-4 7z"/>',flight:'<path d="M10.18 9"/><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>',hotel:'<path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3z"/><path d="M19 7h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z"/>',layers:'<path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74z"/><path d="M12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"/>',layers_clear:'<path d="M19.81 14.99l1.19-.92-1.43-1.43-1.19.92 1.43 1.43z"/><path d="M19.36 10.27L21 9l-9-7-2.91 2.27 7.87 7.88 2.4-1.88z"/><path d="M3.27 1L2 2.27l4.22 4.22L3 9l1.63 1.27L12 16l2.1-1.63 1.43 1.43L12 18.54l-7.37-5.73L3 14.07l9 7 4.95-3.85L20.73 21 22 19.73 3.27 1z"/>',local_activity:'<path d="M20 12c0-1.1.9-2 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-1.99.9-1.99 2v4c1.1 0 1.99.9 1.99 2s-.89 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2zm-4.42 4.8L12 14.5l-3.58 2.3 1.08-4.12-3.29-2.69 4.24-.25L12 5.8l1.54 3.95 4.24.25-3.29 2.69 1.09 4.11z"/>',local_airport:'<path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>',local_atm:'<path d="M11 17h2v-1h1c.55 0 1-.45 1-1v-3c0-.55-.45-1-1-1h-3v-1h4V8h-2V7h-2v1h-1c-.55 0-1 .45-1 1v3c0 .55.45 1 1 1h3v1H9v2h2v1z"/><path d="M20 18H4V6h16v12zm0-14H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2z"/>',local_bar:'<path d="M11 13v6H6v2h12v-2h-5v-6l8-8V3H3v2l8 8zM7.5 7l-2-2h13l-2 2h-9z"/>',local_cafe:'<path d="M20 8h-2V5h2v3zm0-5H4v10c0 2.21 1.79 4 4 4h6c2.21 0 4-1.79 4-4v-3h2c1.11 0 2-.89 2-2V5c0-1.11-.89-2-2-2z"/><path d="M2 21h18v-2H2v2z"/>',local_car_wash:'<path d="M17 5c.83 0 1.5-.67 1.5-1.5 0-1-1.5-2.7-1.5-2.7s-1.5 1.7-1.5 2.7c0 .83.67 1.5 1.5 1.5z"/><path d="M12 5c.83 0 1.5-.67 1.5-1.5 0-1-1.5-2.7-1.5-2.7s-1.5 1.7-1.5 2.7c0 .83.67 1.5 1.5 1.5z"/><path d="M7 5c.83 0 1.5-.67 1.5-1.5C8.5 2.5 7 .8 7 .8S5.5 2.5 5.5 3.5C5.5 4.33 6.17 5 7 5z"/><path d="M5 13l1.5-4.5h11L19 13H5zm12.5 5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm-11 0c-.83 0-1.5-.67-1.5-1.5S5.67 15 6.5 15s1.5.67 1.5 1.5S7.33 18 6.5 18zm12.42-9.99C18.72 7.42 18.16 7 17.5 7h-11c-.66 0-1.21.42-1.42 1.01L3 14v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99z"/>',local_convenience_store:'<path d="M19 7V4H5v3H2v13h8v-4h4v4h8V7h-3zm-8 3H9v1h2v1H8V9h2V8H8V7h3v3zm5 2h-1v-2h-2V7h1v2h1V7h1v5z"/>',local_dining:'<path d="M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 0 0 0 5.66l4.19 4.18z"/><path d="M14.88 11.53c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>',local_drink:'<path d="M3 2l2.01 18.23C5.13 21.23 5.97 22 7 22h10c1.03 0 1.87-.77 1.99-1.77L21 2H3zm9 17c-1.66 0-3-1.34-3-3 0-2 3-5.4 3-5.4s3 3.4 3 5.4c0 1.66-1.34 3-3 3zm6.33-11H5.67l-.44-4h13.53l-.43 4z"/>',local_florist:'<path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9z"/><path d="M12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zm-6.4 4.75c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25z"/><path d="M3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/>',local_gas_station:'<path d="M19.77 7.23l.01-.01-3.72-3.72L15 4.56l2.11 2.11c-.94.36-1.61 1.26-1.61 2.33 0 1.38 1.12 2.5 2.5 2.5.36 0 .69-.08 1-.21v7.21c0 .55-.45 1-1 1s-1-.45-1-1V14c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v16h10v-7.5h1.5v5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V9c0-.69-.28-1.32-.73-1.77zM12 10H6V5h6v5zm6 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>',local_grocery_store:'<path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2z"/><path d="M1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1z"/><path d="M17 18c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>',local_hospital:'<path d="M19 3H5c-1.1 0-1.99.9-1.99 2L3 19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 11h-4v4h-4v-4H6v-4h4V6h4v4h4v4z"/>',local_hotel:'<path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3z"/><path d="M19 7h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z"/>',local_laundry_service:'<path d="M9.17 16.83a4.008 4.008 0 0 0 5.66 0 4.008 4.008 0 0 0 0-5.66l-5.66 5.66z"/><path d="M12 20c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zM7 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm3 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm8-1.99L6 2c-1.11 0-2 .89-2 2v16c0 1.11.89 2 2 2h12c1.11 0 2-.89 2-2V4c0-1.11-.89-1.99-2-1.99z"/>',local_library:'<path d="M12 8c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3z"/><path d="M12 11.55C9.64 9.35 6.48 8 3 8v11c3.48 0 6.64 1.35 9 3.55 2.36-2.19 5.52-3.55 9-3.55V8c-3.48 0-6.64 1.35-9 3.55z"/>',local_mall:'<path d="M19 6h-2c0-2.76-2.24-5-5-5S7 3.24 7 6H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-7-3c1.66 0 3 1.34 3 3H9c0-1.66 1.34-3 3-3zm0 10c-2.76 0-5-2.24-5-5h2c0 1.66 1.34 3 3 3s3-1.34 3-3h2c0 2.76-2.24 5-5 5z"/>',local_movies:'<path d="M18 3v2h-2V3H8v2H6V3H4v18h2v-2h2v2h8v-2h2v2h2V3h-2zM8 17H6v-2h2v2zm0-4H6v-2h2v2zm0-4H6V7h2v2zm10 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z"/>',local_offer:'<path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/>',local_parking:'<path d="M13 3H6v18h4v-6h3c3.31 0 6-2.69 6-6s-2.69-6-6-6zm.2 8H10V7h3.2c1.1 0 2 .9 2 2s-.9 2-2 2z"/>',local_pharmacy:'<path d="M21 5h-2.64l1.14-3.14L17.15 1l-1.46 4H3v2l2 6-2 6v2h18v-2l-2-6 2-6V5zm-5 9h-3v3h-2v-3H8v-2h3V9h2v3h3v2z"/>',local_phone:'<path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>',local_pizza:'<path d="M12 2C8.43 2 5.23 3.54 3.01 6L12 22l8.99-16C18.78 3.55 15.57 2 12 2zM7 7c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm5 8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>',local_play:'<path d="M20 12c0-1.1.9-2 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-1.99.9-1.99 2v4c1.1 0 1.99.9 1.99 2s-.89 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2zm-4.42 4.8L12 14.5l-3.58 2.3 1.08-4.12-3.29-2.69 4.24-.25L12 5.8l1.54 3.95 4.24.25-3.29 2.69 1.09 4.11z"/>',local_post_office:'<path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>',local_print_shop:'<path d="M19 12c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-3 7H8v-5h8v5zm3-11H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3z"/><path d="M18 3H6v4h12V3z"/>',local_restaurant:'<path d="M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 0 0 0 5.66l4.19 4.18z"/><path d="M14.88 11.53c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>',local_see:'<circle cx="12" cy="12" r="3.2"/><path d="M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z"/>',local_shipping:'<path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>',local_taxi:'<path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H15V3H9v2H6.5c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>',map:'<path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z"/>',my_location:'<path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"/><path d="M12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm8.94-8c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06z"/>',navigation:'<path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>',near_me:'<path d="M21 3L3 10.53v.98l6.84 2.65L12.48 21h.98L21 3z"/>',person_pin_circle:'<path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm0 2c1.1 0 2 .9 2 2 0 1.11-.9 2-2 2s-2-.89-2-2c0-1.1.9-2 2-2zm0 10c-1.67 0-3.14-.85-4-2.15.02-1.32 2.67-2.05 4-2.05s3.98.73 4 2.05c-.86 1.3-2.33 2.15-4 2.15z"/>',person_pin:'<path d="M19 2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h4l3 3 3-3h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 3.3c1.49 0 2.7 1.21 2.7 2.7 0 1.49-1.21 2.7-2.7 2.7-1.49 0-2.7-1.21-2.7-2.7 0-1.49 1.21-2.7 2.7-2.7zM18 16H6v-.9c0-2 4-3.1 6-3.1s6 1.1 6 3.1v.9z"/>',pin_drop:'<path d="M10 8c0-1.1.9-2 2-2s2 .9 2 2-.89 2-2 2c-1.1 0-2-.9-2-2zm8 0c0-3.31-2.69-6-6-6S6 4.69 6 8c0 4.5 6 11 6 11s6-6.5 6-11z"/><path d="M5 20v2h14v-2H5z"/>',place:'<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>',rate_review:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 14v-2.47l6.88-6.88c.2-.2.51-.2.71 0l1.77 1.77c.2.2.2.51 0 .71L8.47 14H6zm12 0h-7.5l2-2H18v2z"/>',restaurant:'<path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7z"/><path d="M16 6v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>',restaurant_menu:'<path d="M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 0 0 0 5.66l4.19 4.18z"/><path d="M14.88 11.53c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>',satellite:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM5 4.99h3C8 6.65 6.66 8 5 8V4.99zM5 12v-2c2.76 0 5-2.25 5-5.01h2C12 8.86 8.87 12 5 12zm0 6l3.5-4.5 2.5 3.01L14.5 12l4.5 6H5z"/>',store_mall_directory:'<path d="M20 4H4v2h16V4z"/><path d="M12 18H6v-4h6v4zm9-4v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1z"/>',streetview:'<path d="M12.56 14.33c-.34.27-.56.7-.56 1.17V21h7c1.1 0 2-.9 2-2v-5.98c-.94-.33-1.95-.52-3-.52-2.03 0-3.93.7-5.44 1.83z"/><circle cx="18" cy="6" r="5"/><path d="M11.5 6c0-1.08.27-2.1.74-3H5c-1.1 0-2 .9-2 2v14c0 .55.23 1.05.59 1.41l9.82-9.82C12.23 9.42 11.5 7.8 11.5 6z"/>',subway:'<circle cx="15.5" cy="16" r="1"/><circle cx="8.5" cy="16" r="1"/><path d="M7.01 9h10v5h-10z"/><path d="M17.8 2.8C16 2.09 13.86 2 12 2c-1.86 0-4 .09-5.8.8C3.53 3.84 2 6.05 2 8.86V22h20V8.86c0-2.81-1.53-5.02-4.2-6.06zm.2 13.08c0 1.45-1.18 2.62-2.63 2.62l1.13 1.12V20H15l-1.5-1.5h-2.83L9.17 20H7.5v-.38l1.12-1.12C7.18 18.5 6 17.32 6 15.88V9c0-2.63 3-3 6-3 3.32 0 6 .38 6 3v6.88z"/>',terrain:'<path d="M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z"/>',traffic:'<path d="M20 10h-3V8.86c1.72-.45 3-2 3-3.86h-3V4c0-.55-.45-1-1-1H8c-.55 0-1 .45-1 1v1H4c0 1.86 1.28 3.41 3 3.86V10H4c0 1.86 1.28 3.41 3 3.86V15H4c0 1.86 1.28 3.41 3 3.86V20c0 .55.45 1 1 1h8c.55 0 1-.45 1-1v-1.14c1.72-.45 3-2 3-3.86h-3v-1.14c1.72-.45 3-2 3-3.86zm-8 9c-1.11 0-2-.9-2-2s.89-2 2-2c1.1 0 2 .9 2 2s-.89 2-2 2zm0-5c-1.11 0-2-.9-2-2s.89-2 2-2c1.1 0 2 .9 2 2s-.89 2-2 2zm0-5c-1.11 0-2-.9-2-2 0-1.11.89-2 2-2 1.1 0 2 .89 2 2 0 1.1-.89 2-2 2z"/>',train:'<path d="M12 2c-4 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h2.23l2-2H14l2 2h2v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-7H6V6h5v4zm2 0V6h5v4h-5zm3.5 7c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>',tram:'<path d="M19 16.94V8.5c0-2.79-2.61-3.4-6.01-3.49l.76-1.51H17V2H7v1.5h4.75l-.76 1.52C7.86 5.11 5 5.73 5 8.5v8.44c0 1.45 1.19 2.66 2.59 2.97L6 21.5v.5h2.23l2-2H14l2 2h2v-.5L16.5 20h-.08c1.69 0 2.58-1.37 2.58-3.06zm-7 1.56c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm5-4.5H7V9h10v5z"/>',transfer_within_a_station:'<path d="M16.49 15.5v-1.75L14 16.25l2.49 2.5V17H22v-1.5z"/><path d="M19.51 19.75H14v1.5h5.51V23L22 20.5 19.51 18z"/><path d="M9.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5.75 8.9L3 23h2.1l1.75-8L9 17v6h2v-7.55L8.95 13.4l.6-3C10.85 12 12.8 13 15 13v-2c-1.85 0-3.45-1-4.35-2.45l-.95-1.6C9.35 6.35 8.7 6 8 6c-.25 0-.5.05-.75.15L2 8.3V13h2V9.65l1.75-.75"/>',zoom_out_map:'<path d="M15 3l2.3 2.3-2.89 2.87 1.42 1.42L18.7 6.7 21 9V3z"/><path d="M3 9l2.3-2.3 2.87 2.89 1.42-1.42L6.7 5.3 9 3H3z"/><path d="M9 21l-2.3-2.3 2.89-2.87-1.42-1.42L5.3 17.3 3 15v6z"/><path d="M21 15l-2.3 2.3-2.87-2.89-1.42 1.42 2.89 2.87L15 21h6z"/>',apps:'<path d="M4 8h4V4H4v4z"/><path d="M10 20h4v-4h-4v4z"/><path d="M4 20h4v-4H4v4z"/><path d="M4 14h4v-4H4v4z"/><path d="M10 14h4v-4h-4v4z"/><path d="M16 4v4h4V4h-4z"/><path d="M10 8h4V4h-4v4z"/><path d="M16 14h4v-4h-4v4z"/><path d="M16 20h4v-4h-4v4z"/>',arrow_back:'<path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>',arrow_downward:'<path d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z"/>',arrow_drop_down:'<path d="M7 10l5 5 5-5z"/>',arrow_drop_down_circle:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 12l-4-4h8l-4 4z"/>',arrow_drop_up:'<path d="M7 14l5-5 5 5z"/>',arrow_forward:'<path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>',arrow_upwards:'<path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"/>',cancel:'<path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>',check:'<path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>',chevron_left:'<path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>',chevron_right:'<path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>',close:'<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>',expand_less:'<path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z"/>',expand_more:'<path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"/>',first_page:'<path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6z"/><path d="M6 6h2v12H6z"/>',fullscreen:'<path d="M7 14H5v5h5v-2H7v-3z"/><path d="M5 10h2V7h3V5H5v5z"/><path d="M17 17h-3v2h5v-5h-2v3z"/><path d="M14 5v2h3v3h2V5h-5z"/>',fullscreen_exit:'<path d="M5 16h3v3h2v-5H5v2z"/><path d="M8 8H5v2h5V5H8v3z"/><path d="M14 19h2v-3h3v-2h-5v5z"/><path d="M16 8V5h-2v5h5V8h-3z"/>',last_page:'<path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6z"/><path d="M16 6h2v12h-2z"/>',menu:'<path d="M3 18h18v-2H3v2z"/><path d="M3 13h18v-2H3v2z"/><path d="M3 6v2h18V6H3z"/>',more_horiz:'<path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M18 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>',more_vert:'<path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/><path d="M12 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/><path d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>',refresh:'<path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>',subdirectory_arrow_left:' <path d="M11 9l1.42 1.42L8.83 14H18V4h2v12H8.83l3.59 3.58L11 21l-6-6 6-6z"/>',subdirectory_arrow_right:'<path d="M19 15l-6 6-1.42-1.42L15.17 16H4V4h2v10h9.17l-3.59-3.58L13 9l6 6z"/>',adb:'<path d="M5 16c0 3.87 3.13 7 7 7s7-3.13 7-7v-4H5v4z"/><path d="M15 9c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zM9 9c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm7.12-4.63l2.1-2.1-.82-.83-2.3 2.31C14.16 3.28 13.12 3 12 3s-2.16.28-3.09.75L6.6 1.44l-.82.83 2.1 2.1C6.14 5.64 5 7.68 5 10v1h14v-1c0-2.32-1.14-4.36-2.88-5.63z"/>',airline_seat_flat:'<path d="M22 11v2H9V7h9c2.21 0 4 1.79 4 4z"/><path d="M2 14v2h6v2h8v-2h6v-2H2z"/><path d="M7.14 12.1a3 3 0 0 0-.04-4.24 3 3 0 0 0-4.24.04 3 3 0 0 0 .04 4.24 3 3 0 0 0 4.24-.04z"/>',airline_seat_angled:'<path d="M22.25 14.29l-.69 1.89L9.2 11.71l2.08-5.66 8.56 3.09a4 4 0 0 1 2.41 5.15z"/><path d="M1.5 12.14L8 14.48V19h8v-1.63L20.52 19l.69-1.89-19.02-6.86-.69 1.89z"/><path d="M7.3 10.2a3.01 3.01 0 0 0 1.41-4A3.005 3.005 0 0 0 4.7 4.8a2.99 2.99 0 0 0-1.4 4 2.99 2.99 0 0 0 4 1.4z"/>',airline_seat_individual_suite:'<path d="M7 13c1.65 0 3-1.35 3-3S8.65 7 7 7s-3 1.35-3 3 1.35 3 3 3z"/><path d="M19 7h-8v7H3V7H1v10h22v-6c0-2.21-1.79-4-4-4z"/>',airline_seat_legroom_extra:'<path d="M4 12V3H2v9c0 2.76 2.24 5 5 5h6v-2H7c-1.66 0-3-1.34-3-3z"/><path d="M22.83 17.24c-.38-.72-1.29-.97-2.03-.63l-1.09.5-3.41-6.98a2.01 2.01 0 0 0-1.79-1.12L11 9V3H5v8c0 1.66 1.34 3 3 3h7l3.41 7 3.72-1.7c.77-.36 1.1-1.3.7-2.06z"/>',airline_seat_legroom_normal:'<path d="M5 12V3H3v9c0 2.76 2.24 5 5 5h6v-2H8c-1.66 0-3-1.34-3-3z"/><path d="M20.5 18H19v-7c0-1.1-.9-2-2-2h-5V3H6v8c0 1.65 1.35 3 3 3h7v7h4.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5z"/>',airline_seat_legroom_reduced:'<path d="M19.97 19.2c.18.96-.55 1.8-1.47 1.8H14v-3l1-4H9c-1.65 0-3-1.35-3-3V3h6v6h5c1.1 0 2 .9 2 2l-2 7h1.44c.73 0 1.39.49 1.53 1.2z"/><path d="M5 12V3H3v9c0 2.76 2.24 5 5 5h4v-2H8c-1.66 0-3-1.34-3-3z"/>',airline_seat_recline_extra:'<path d="M5.35 5.64c-.9-.64-1.12-1.88-.49-2.79.63-.9 1.88-1.12 2.79-.49.9.64 1.12 1.88.49 2.79-.64.9-1.88 1.12-2.79.49z"/><path d="M16 19H8.93c-1.48 0-2.74-1.08-2.96-2.54L4 7H2l1.99 9.76A5.01 5.01 0 0 0 8.94 21H16v-2z"/><path d="M16.23 15h-4.88l-1.03-4.1c1.58.89 3.28 1.54 5.15 1.22V9.99c-1.63.31-3.44-.27-4.69-1.25L9.14 7.47c-.23-.18-.49-.3-.76-.38a2.21 2.21 0 0 0-.99-.06h-.02a2.268 2.268 0 0 0-1.84 2.61l1.35 5.92A3.008 3.008 0 0 0 9.83 18h6.85l3.82 3 1.5-1.5-5.77-4.5z"/>',airline_seat_recline_normal:'<path d="M7.59 5.41c-.78-.78-.78-2.05 0-2.83.78-.78 2.05-.78 2.83 0 .78.78.78 2.05 0 2.83-.79.79-2.05.79-2.83 0z"/><path d="M6 16V7H4v9c0 2.76 2.24 5 5 5h6v-2H9c-1.66 0-3-1.34-3-3z"/><path d="M20 20.07L14.93 15H11.5v-3.68c1.4 1.15 3.6 2.16 5.5 2.16v-2.16c-1.66.02-3.61-.87-4.67-2.04l-1.4-1.55c-.19-.21-.43-.38-.69-.5-.29-.14-.62-.23-.96-.23h-.03C8.01 7 7 8.01 7 9.25V15c0 1.66 1.34 3 3 3h5.07l3.5 3.5L20 20.07z"/>',bluetooth_audio:'<path d="M19.53 6.71l-1.26 1.26c.63 1.21.98 2.57.98 4.02 0 1.45-.36 2.82-.98 4.02l1.2 1.2c.97-1.54 1.54-3.36 1.54-5.31-.01-1.89-.55-3.67-1.48-5.19zm-5.29 5.3l2.32 2.32c.28-.72.44-1.51.44-2.33 0-.82-.16-1.59-.43-2.31l-2.33 2.32z"/><path d="M12.88 16.29L11 18.17v-3.76l1.88 1.88zM11 5.83l1.88 1.88L11 9.59V5.83zm4.71 1.88L10 2H9v7.59L4.41 5 3 6.41 8.59 12 3 17.59 4.41 19 9 14.41V22h1l5.71-5.71-4.3-4.29 4.3-4.29z"/>',confirmation_number:'<path d="M22 10V6c0-1.11-.9-2-2-2H4c-1.1 0-1.99.89-1.99 2v4c1.1 0 1.99.9 1.99 2s-.89 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2s.9-2 2-2zm-9 7.5h-2v-2h2v2zm0-4.5h-2v-2h2v2zm0-4.5h-2v-2h2v2z"/>',disc_full:'<path d="M20 7v5h2V7h-2zm0 9h2v-2h-2v2z"/><path d="M10 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0-10c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8z"/>',do_not_disturb:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z"/>',do_not_disturb_alt:'<path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zM4 12c0-4.4 3.6-8 8-8 1.8 0 3.5.6 4.9 1.7L5.7 16.9C4.6 15.5 4 13.8 4 12zm8 8c-1.8 0-3.5-.6-4.9-1.7L18.3 7.1C19.4 8.5 20 10.2 20 12c0 4.4-3.6 8-8 8z"/>',do_not_disturb_off:'<path d="M17 11v2h-1.46l4.68 4.68A9.92 9.92 0 0 0 22 12c0-5.52-4.48-10-10-10-2.11 0-4.07.66-5.68 1.78L13.54 11H17z"/><path d="M7 13v-2h1.46l2 2H7zM2.27 2.27L1 3.54l2.78 2.78A9.92 9.92 0 0 0 2 12c0 5.52 4.48 10 10 10 2.11 0 4.07-.66 5.68-1.78L20.46 23l1.27-1.27L11 11 2.27 2.27z"/>',do_not_disturb_on:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/>',drive_eta:'<path d="M18.92 5.01C18.72 4.42 18.16 4 17.5 4h-11c-.66 0-1.21.42-1.42 1.01L3 11v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 15c-.83 0-1.5-.67-1.5-1.5S5.67 12 6.5 12s1.5.67 1.5 1.5S7.33 15 6.5 15zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 10l1.5-4.5h11L19 10H5z"/>',enhanced_encryption:'<path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM8.9 6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2H8.9V6zM16 16h-3v3h-2v-3H8v-2h3v-3h2v3h3v2z"/>',event_available:'<path d="M16.53 11.06L15.47 10l-4.88 4.88-2.12-2.12-1.06 1.06L10.59 17l5.94-5.94z"/><path d="M19 19H5V8h14v11zm0-16h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',event_busy:'<path d="M9.31 17l2.44-2.44L14.19 17l1.06-1.06-2.44-2.44 2.44-2.44L14.19 10l-2.44 2.44L9.31 10l-1.06 1.06 2.44 2.44-2.44 2.44L9.31 17z"/><path d="M19 19H5V8h14v11zm0-16h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',event_note:'<path d="M19 19H5V8h14v11zm0-16h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/><path d="M14 14H7v2h7v-2zm3-4H7v2h10v-2z"/>',folder_special:'<path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6.42 12L10 15.9 6.42 18l.95-4.07-3.16-2.74 4.16-.36L10 7l1.63 3.84 4.16.36-3.16 2.74.95 4.06z"/>',live_tv:'<path d="M21 20H3V8h18v12zm0-14h-7.59l3.29-3.29L16 2l-4 4-4-4-.71.71L10.59 6H3a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8a2 2 0 0 0-2-2z"/><path d="M9 10v8l7-4z"/>',mms:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM5 14l3.5-4.5 2.5 3.01L14.5 8l4.5 6H5z"/>',more:'<path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.97.89 1.66.89H22c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 13.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm5 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm5 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>',network_check:'<path d="M15.9 5c-.17 0-.32.09-.41.23l-.07.15-5.18 11.65c-.16.29-.26.61-.26.96 0 1.11.9 2.01 2.01 2.01.96 0 1.77-.68 1.96-1.59l.01-.03L16.4 5.5c0-.28-.22-.5-.5-.5z"/><path d="M21 11l2-2a15.367 15.367 0 0 0-5.59-3.57l-.53 2.82c1.5.62 2.9 1.53 4.12 2.75zM1 9l2 2c2.88-2.88 6.79-4.08 10.53-3.62l1.19-2.68C9.89 3.84 4.74 5.27 1 9z"/><path d="M5 13l2 2a7.1 7.1 0 0 1 4.03-2l1.28-2.88c-2.63-.08-5.3.87-7.31 2.88zm12 2l2-2c-.8-.8-1.7-1.42-2.66-1.89l-.55 2.92c.42.27.83.59 1.21.97z"/>',network_locked:'<path d="M19.5 10c.17 0 .33.03.5.05V1L1 20h13v-3c0-.89.39-1.68 1-2.23v-.27c0-2.48 2.02-4.5 4.5-4.5z"/><path d="M21 16h-3v-1.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V16zm1 0v-1.5c0-1.38-1.12-2.5-2.5-2.5S17 13.12 17 14.5V16c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h5c.55 0 1-.45 1-1v-4c0-.55-.45-1-1-1z"/>',no_encryption:'<path d="M21 21.78L4.22 5 3 6.22l2.04 2.04C4.42 8.6 4 9.25 4 10v10c0 1.1.9 2 2 2h12c.23 0 .45-.05.66-.12L19.78 23 21 21.78z"/><path d="M8.9 6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H9.66L20 18.34V10c0-1.1-.9-2-2-2h-1V6c0-2.76-2.24-5-5-5-2.56 0-4.64 1.93-4.94 4.4L8.9 7.24V6z"/>',ondemand_video:'<path d="M21 17H3V5h18v12zm0-14H3c-1.11 0-2 .89-2 2v12a2 2 0 0 0 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5a2 2 0 0 0-2-2z"/><path d="M16 11l-7 4V7z"/>',personal_video:'<path d="M21 3H3c-1.11 0-2 .89-2 2v12c0 1.1.89 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.11-.9-2-2-2zm0 14H3V5h18v12z"/>',
phone_bluetooth_speaker:'<path d="M18 7.21l.94.94-.94.94V7.21zm0-4.3l.94.94-.94.94V2.91zM14.71 9.5L17 7.21V11h.5l2.85-2.85L18.21 6l2.15-2.15L17.5 1H17v3.79L14.71 2.5l-.71.71L16.79 6 14 8.79l.71.71z"/><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.59l2.2-2.21c.28-.26.36-.65.25-1C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"/>',phone_forwarded:'<path d="M18 11l5-5-5-5v3h-4v4h4v3z"/><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.59l2.2-2.21c.28-.26.36-.65.25-1C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"/>',phone_in_talk:'<path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.59l2.2-2.21c.28-.26.36-.65.25-1C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"/><path d="M19 12h2c0-4.97-4.03-9-9-9v2c3.87 0 7 3.13 7 7z"/><path d="M15 12h2c0-2.76-2.24-5-5-5v2c1.66 0 3 1.34 3 3z"/>',phone_locked:'<path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.59l2.2-2.21c.28-.26.36-.65.25-1C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"/><path d="M19.2 4h-3.4v-.5c0-.94.76-1.7 1.7-1.7s1.7.76 1.7 1.7V4zm.8 0v-.5C20 2.12 18.88 1 17.5 1S15 2.12 15 3.5V4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h5c.55 0 1-.45 1-1V5c0-.55-.45-1-1-1z"/>',phone_missed:'<path d="M6.5 5.5L12 11l7-7-1-1-6 6-4.5-4.5H11V3H5v6h1.5V5.5z"/><path d="M23.71 16.67C20.66 13.78 16.54 12 12 12 7.46 12 3.34 13.78.29 16.67c-.18.18-.29.43-.29.71 0 .28.11.53.29.71l2.48 2.48c.18.18.43.29.71.29.27 0 .52-.11.7-.28.79-.74 1.69-1.36 2.66-1.85.33-.16.56-.5.56-.9v-3.1c1.45-.48 3-.73 4.6-.73 1.6 0 3.15.25 4.6.72v3.1c0 .39.23.74.56.9.98.49 1.87 1.12 2.67 1.85.18.18.43.28.7.28.28 0 .53-.11.71-.29l2.48-2.48c.18-.18.29-.43.29-.71 0-.28-.12-.52-.3-.7z"/>',phone_paused:'<path d="M17 3h-2v7h2V3z"/><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.59l2.2-2.21c.28-.26.36-.65.25-1C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"/><path d="M19 3v7h2V3h-2z"/>',power:'<path d="M16.01 7L16 3h-2v4h-4V3H8v4h-.01C7 6.99 6 7.99 6 8.99v5.49L9.5 18v3h5v-3l3.5-3.51v-5.5c0-1-1-2-1.99-1.99z"/>',priority_high:'<circle cx="12" cy="19" r="2"/><path d="M10 3h4v12h-4z"/>',sd_card:'<path d="M18 2h-8L4.02 8 4 20c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-6 6h-2V4h2v4zm3 0h-2V4h2v4zm3 0h-2V4h2v4z"/>',sim_card_alert:'<path d="M18 2h-8L4.02 8 4 20c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-5 15h-2v-2h2v2zm0-4h-2V8h2v5z"/>',sms:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 11H7V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z"/>',sms_failed:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z"/>',sync:'<path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8z"/><path d="M12 18c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>',sync_disabled:'<path d="M10 6.35V4.26c-.8.21-1.55.54-2.23.96l1.46 1.46c.25-.12.5-.24.77-.33z"/><path d="M2.86 5.41l2.36 2.36a7.925 7.925 0 0 0 1.14 9.87L4 20h6v-6l-2.24 2.24A6.003 6.003 0 0 1 6 12c0-1 .25-1.94.68-2.77l8.08 8.08c-.25.13-.5.25-.77.34v2.09c.8-.21 1.55-.54 2.23-.96l2.36 2.36 1.27-1.27L4.14 4.14 2.86 5.41z"/><path d="M20 4h-6v6l2.24-2.24A6.003 6.003 0 0 1 18 12c0 1-.25 1.94-.68 2.77l1.46 1.46a7.925 7.925 0 0 0-1.14-9.87L20 4z"/>',sync_problem:'<path d="M3 12c0 2.21.91 4.2 2.36 5.64L3 20h6v-6l-2.24 2.24C5.68 15.15 5 13.66 5 12c0-2.61 1.67-4.83 4-5.65V4.26C5.55 5.15 3 8.27 3 12z"/><path d="M21 4h-6v6l2.24-2.24C18.32 8.85 19 10.34 19 12c0 2.61-1.67 4.83-4 5.65v2.09c3.45-.89 6-4.01 6-7.74 0-2.21-.91-4.2-2.36-5.64L21 4z"/><path d="M11 13h2V7h-2v6zm0 4h2v-2h-2v2z"/>',system_update:'<path d="M17 19H7V5h10v14zm0-17.99L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99z"/><path d="M16 13h-3V8h-2v5H8l4 4 4-4z"/>',tap_and_play:'<path d="M2 16v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7z"/><path d="M2 20v3h3c0-1.66-1.34-3-3-3z"/><path d="M2 12v2a9 9 0 0 1 9 9h2c0-6.08-4.92-11-11-11z"/><path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v7.37c.69.16 1.36.37 2 .64V5h10v13h-3.03c.52 1.25.84 2.59.95 4H17c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99z"/>',time_to_leave:'<path d="M18.92 5.01C18.72 4.42 18.16 4 17.5 4h-11c-.66 0-1.21.42-1.42 1.01L3 11v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 15c-.83 0-1.5-.67-1.5-1.5S5.67 12 6.5 12s1.5.67 1.5 1.5S7.33 15 6.5 15zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 10l1.5-4.5h11L19 10H5z"/>',vibration:'<path d="M0 15h2V9H0v6z"/><path d="M3 17h2V7H3v10z"/><path d="M22 9v6h2V9h-2z"/><path d="M19 17h2V7h-2v10z"/><path d="M16 19H8V5h8v14zm.5-16h-9C6.67 3 6 3.67 6 4.5v15c0 .83.67 1.5 1.5 1.5h9c.83 0 1.5-.67 1.5-1.5v-15c0-.83-.67-1.5-1.5-1.5z"/>',voice_chat:'<path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12l-4-3.2V14H6V6h8v3.2L18 6v8z"/>',vpn_lock:'<path d="M21.2 4h-3.4v-.5c0-.94.76-1.7 1.7-1.7s1.7.76 1.7 1.7V4zm.8 0v-.5a2.5 2.5 0 0 0-5 0V4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h5c.55 0 1-.45 1-1V5c0-.55-.45-1-1-1z"/><path d="M10 20.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L8 16v1c0 1.1.9 2 2 2v1.93zM18.92 12c.04.33.08.66.08 1 0 2.08-.8 3.97-2.1 5.39-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H7v-2h2c.55 0 1-.45 1-1V8h2c1.1 0 2-.9 2-2V3.46c-.95-.3-1.95-.46-3-.46C5.48 3 1 7.48 1 13s4.48 10 10 10 10-4.48 10-10c0-.34-.02-.67-.05-1h-2.03z"/>',wc:'<path d="M5.5 22v-7.5H4V9c0-1.1.9-2 2-2h3c1.1 0 2 .9 2 2v5.5H9.5V22h-4z"/><path d="M18 22v-6h3l-2.54-7.63A2.01 2.01 0 0 0 16.56 7h-.12a2 2 0 0 0-1.9 1.37L12 16h3v6h3z"/><path d="M7.5 6c1.11 0 2-.89 2-2 0-1.11-.89-2-2-2-1.11 0-2 .89-2 2 0 1.11.89 2 2 2z"/><path d="M16.5 6c1.11 0 2-.89 2-2 0-1.11-.89-2-2-2-1.11 0-2 .89-2 2 0 1.11.89 2 2 2z"/>',wifi:'<path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9z"/><path d="M9 17l3 3 3-3a4.237 4.237 0 0 0-6 0z"/><path d="M5 13l2 2a7.074 7.074 0 0 1 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>',ac_unit:'<path d="M22 11h-4.17l3.24-3.24-1.41-1.42L15 11h-2V9l4.66-4.66-1.42-1.41L13 6.17V2h-2v4.17L7.76 2.93 6.34 4.34 11 9v2H9L4.34 6.34 2.93 7.76 6.17 11H2v2h4.17l-3.24 3.24 1.41 1.42L9 13h2v2l-4.66 4.66 1.42 1.41L11 17.83V22h2v-4.17l3.24 3.24 1.42-1.41L13 15v-2h2l4.66 4.66 1.41-1.42L17.83 13H22z"/>',airport_shuttle:'<path d="M17 5H3c-1.1 0-2 .89-2 2v9h2c0 1.65 1.34 3 3 3s3-1.35 3-3h5.5c0 1.65 1.34 3 3 3s3-1.35 3-3H23v-5l-6-6zM3 11V7h4v4H3zm3 6.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm7-6.5H9V7h4v4zm4.5 6.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM15 11V7h1l4 4h-5z"/>',all_inclusive:'<path d="M18.6 6.62c-1.44 0-2.8.56-3.77 1.53L12 10.66 10.48 12h.01L7.8 14.39c-.64.64-1.49.99-2.4.99-1.87 0-3.39-1.51-3.39-3.38S3.53 8.62 5.4 8.62c.91 0 1.76.35 2.44 1.03l1.13 1 1.51-1.34L9.22 8.2C8.2 7.18 6.84 6.62 5.4 6.62 2.42 6.62 0 9.04 0 12s2.42 5.38 5.4 5.38c1.44 0 2.8-.56 3.77-1.53l2.83-2.5.01.01L13.52 12h-.01l2.69-2.39c.64-.64 1.49-.99 2.4-.99 1.87 0 3.39 1.51 3.39 3.38s-1.52 3.38-3.39 3.38c-.9 0-1.76-.35-2.44-1.03l-1.14-1.01-1.51 1.34 1.27 1.12c1.02 1.01 2.37 1.57 3.82 1.57 2.98 0 5.4-2.41 5.4-5.38s-2.42-5.37-5.4-5.37z"/>',beach_access:'<path d="M13.127 14.56l1.43-1.43 6.44 6.443L19.57 21z"/><path d="M17.42 8.83l2.86-2.86c-3.95-3.95-10.35-3.96-14.3-.02 3.93-1.3 8.31-.25 11.44 2.88z"/><path d="M5.95 5.98c-3.94 3.95-3.93 10.35.02 14.3l2.86-2.86C5.7 14.29 4.65 9.91 5.95 5.98z"/><path d="M5.97 5.96l-.01.01c-.38 3.01 1.17 6.88 4.3 10.02l5.73-5.73c-3.13-3.13-7.01-4.68-10.02-4.3z"/>',business_center:'<path d="M10 16v-1H3.01L3 19c0 1.11.89 2 2 2h14c1.11 0 2-.89 2-2v-4h-7v1h-4z"/><path d="M14 7h-4V5h4v2zm6 0h-4.01V5l-2-2h-4l-2 2v2H4c-1.1 0-2 .9-2 2v3c0 1.11.89 2 2 2h6v-2h4v2h6c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2z"/>',casino:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM7.5 18c-.83 0-1.5-.67-1.5-1.5S6.67 15 7.5 15s1.5.67 1.5 1.5S8.33 18 7.5 18zm0-9C6.67 9 6 8.33 6 7.5S6.67 6 7.5 6 9 6.67 9 7.5 8.33 9 7.5 9zm4.5 4.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5 4.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm0-9c-.83 0-1.5-.67-1.5-1.5S15.67 6 16.5 6s1.5.67 1.5 1.5S17.33 9 16.5 9z"/>',child_care:'<circle cx="14.5" cy="10.5" r="1.25"/> <circle cx="9.5" cy="10.5" r="1.25"/> <path d="M22.94 12.66c.04-.21.06-.43.06-.66s-.02-.45-.06-.66c-.25-1.51-1.36-2.74-2.81-3.17-.53-1.12-1.28-2.1-2.19-2.91C16.36 3.85 14.28 3 12 3s-4.36.85-5.94 2.26c-.92.81-1.67 1.8-2.19 2.91-1.45.43-2.56 1.65-2.81 3.17-.04.21-.06.43-.06.66s.02.45.06.66c.25 1.51 1.36 2.74 2.81 3.17.52 1.11 1.27 2.09 2.17 2.89C7.62 20.14 9.71 21 12 21s4.38-.86 5.97-2.28c.9-.8 1.65-1.79 2.17-2.89 1.44-.43 2.55-1.65 2.8-3.17zM19 14c-.1 0-.19-.02-.29-.03-.2.67-.49 1.29-.86 1.86C16.6 17.74 14.45 19 12 19s-4.6-1.26-5.85-3.17c-.37-.57-.66-1.19-.86-1.86-.1.01-.19.03-.29.03-1.1 0-2-.9-2-2s.9-2 2-2c.1 0 .19.02.29.03.2-.67.49-1.29.86-1.86C7.4 6.26 9.55 5 12 5s4.6 1.26 5.85 3.17c.37.57.66 1.19.86 1.86.1-.01.19-.03.29-.03 1.1 0 2 .9 2 2s-.9 2-2 2zM7.5 14c.76 1.77 2.49 3 4.5 3s3.74-1.23 4.5-3h-9z"/>',child_friedly:'<path d="M13 2v8h8c0-4.42-3.58-8-8-8z"/><path d="M17 20c-.83 0-1.5-.67-1.5-1.5S16.17 17 17 17s1.5.67 1.5 1.5S17.83 20 17 20zm-9 0c-.83 0-1.5-.67-1.5-1.5S7.17 17 8 17s1.5.67 1.5 1.5S8.83 20 8 20zm11.32-4.11A7.948 7.948 0 0 0 21 11H6.44l-.95-2H2v2h2.22s1.89 4.07 2.12 4.42c-1.1.59-1.84 1.75-1.84 3.08C4.5 20.43 6.07 22 8 22c1.76 0 3.22-1.3 3.46-3h2.08c.24 1.7 1.7 3 3.46 3 1.93 0 3.5-1.57 3.5-3.5 0-1.04-.46-1.97-1.18-2.61z"/>',fitness_center:'<path d="M20.57 14.86L22 13.43 20.57 12 17 15.57 8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2.71 2.71 4.14l1.43 1.43L2 7.71l1.43 1.43L2 10.57 3.43 12 7 8.43 15.57 17 12 20.57 13.43 22l1.43-1.43L16.29 22l2.14-2.14 1.43 1.43 1.43-1.43-1.43-1.43L22 16.29z"/>',free_breakfast:'<path d="M20 8h-2V5h2v3zm0-5H4v10c0 2.21 1.79 4 4 4h6c2.21 0 4-1.79 4-4v-3h2a2 2 0 0 0 2-2V5c0-1.11-.89-2-2-2z"/><path d="M4 19h16v2H4z"/>',golf_course:'<circle cx="19.5" cy="19.5" r="1.5"/><path d="M17 5.92L9 2v18H7v-1.73c-1.79.35-3 .99-3 1.73 0 1.1 2.69 2 6 2s6-.9 6-2c0-.99-2.16-1.81-5-1.97V8.98l6-3.06z"/>',hot_tub:'<circle cx="7" cy="6" r="2"/><path d="M11.15 12c-.31-.22-.59-.46-.82-.72l-1.4-1.55c-.19-.21-.43-.38-.69-.5-.29-.14-.62-.23-.96-.23h-.03C6.01 9 5 10.01 5 11.25V12H2v8c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-8H11.15zM7 20H5v-6h2v6zm4 0H9v-6h2v6zm4 0h-2v-6h2v6zm4 0h-2v-6h2v6zm-.35-14.14l-.07-.07c-.57-.62-.82-1.41-.67-2.2L18 3h-1.89l-.06.43c-.2 1.36.27 2.71 1.3 3.72l.07.06c.57.62.82 1.41.67 2.2l-.11.59h1.91l.06-.43c.21-1.36-.27-2.71-1.3-3.71zm-4 0l-.07-.07c-.57-.62-.82-1.41-.67-2.2L14 3h-1.89l-.06.43c-.2 1.36.27 2.71 1.3 3.72l.07.06c.57.62.82 1.41.67 2.2l-.11.59h1.91l.06-.43c.21-1.36-.27-2.71-1.3-3.71z"/>',kitchen:'<path d="M18 9H6V4h12v5zm0 11H6v-9.02h12V20zm0-17.99L6 2a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.11-.9-1.99-2-1.99z"/><path d="M8 5h2v3H8z"/><path d="M8 12h2v5H8z"/>',pool:'<path d="M22 21c-1.11 0-1.73-.37-2.18-.64-.37-.22-.6-.36-1.15-.36-.56 0-.78.13-1.15.36-.46.27-1.07.64-2.18.64s-1.73-.37-2.18-.64c-.37-.22-.6-.36-1.15-.36-.56 0-.78.13-1.15.36-.46.27-1.08.64-2.19.64-1.11 0-1.73-.37-2.18-.64-.37-.23-.6-.36-1.15-.36s-.78.13-1.15.36c-.46.27-1.08.64-2.19.64v-2c.56 0 .78-.13 1.15-.36.46-.27 1.08-.64 2.19-.64s1.73.37 2.18.64c.37.23.59.36 1.15.36.56 0 .78-.13 1.15-.36.46-.27 1.08-.64 2.19-.64 1.11 0 1.73.37 2.18.64.37.22.6.36 1.15.36s.78-.13 1.15-.36c.45-.27 1.07-.64 2.18-.64s1.73.37 2.18.64c.37.23.59.36 1.15.36v2zm0-4.5c-1.11 0-1.73-.37-2.18-.64-.37-.22-.6-.36-1.15-.36-.56 0-.78.13-1.15.36-.45.27-1.07.64-2.18.64s-1.73-.37-2.18-.64c-.37-.22-.6-.36-1.15-.36-.56 0-.78.13-1.15.36-.45.27-1.07.64-2.18.64s-1.73-.37-2.18-.64c-.37-.22-.6-.36-1.15-.36s-.78.13-1.15.36c-.47.27-1.09.64-2.2.64v-2c.56 0 .78-.13 1.15-.36.45-.27 1.07-.64 2.18-.64s1.73.37 2.18.64c.37.22.6.36 1.15.36.56 0 .78-.13 1.15-.36.45-.27 1.07-.64 2.18-.64s1.73.37 2.18.64c.37.22.6.36 1.15.36s.78-.13 1.15-.36c.45-.27 1.07-.64 2.18-.64s1.73.37 2.18.64c.37.22.6.36 1.15.36v2zM8.67 12c.56 0 .78-.13 1.15-.36.46-.27 1.08-.64 2.19-.64 1.11 0 1.73.37 2.18.64.37.22.6.36 1.15.36s.78-.13 1.15-.36c.12-.07.26-.15.41-.23L10.48 5C8.93 3.45 7.5 2.99 5 3v2.5c1.82-.01 2.89.39 4 1.5l1 1-3.25 3.25c.31.12.56.27.77.39.37.23.59.36 1.15.36z"/> <circle cx="16.5" cy="5.5" r="2.5"/>',room_service:'<path d="M2 17h20v2H2z"/><path d="M13.84 7.79A2.006 2.006 0 0 0 12 5a2.006 2.006 0 0 0-1.84 2.79C6.25 8.6 3.27 11.93 3 16h18c-.27-4.07-3.25-7.4-7.16-8.21z"/>',rv_hookup:'<path d="M18 14h-4v-3h4v3zm-7 6c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm9-3v-6c0-1.1-.9-2-2-2H7V7l-3 3 3 3v-2h4v3H4v3c0 1.1.9 2 2 2h2c0 1.66 1.34 3 3 3s3-1.34 3-3h8v-2h-2z"/><path d="M17 2v2H9v2h8v2l3-3z"/>',smoke_free:'<path d="M2 6l6.99 7H2v3h9.99l7 7 1.26-1.25-17-17z"/><path d="M20.5 13H22v3h-1.5z"/><path d="M18 13h1.5v3H18z"/><path d="M18.85 4.88c.62-.61 1-1.45 1-2.38h-1.5c0 1.02-.83 1.85-1.85 1.85v1.5c2.24 0 4 1.83 4 4.07V12H22V9.92c0-2.23-1.28-4.15-3.15-5.04z"/><path d="M14.5 8.7h1.53c1.05 0 1.97.74 1.97 2.05V12h1.5v-1.59c0-1.8-1.6-3.16-3.47-3.16H14.5c-1.02 0-1.85-.98-1.85-2s.83-1.75 1.85-1.75V2a3.35 3.35 0 0 0 0 6.7z"/><path d="M17 15.93V13h-2.93z"/>',smoke_rooms:'<path d="M2 16h15v3H2z"/><path d="M20.5 16H22v3h-1.5z"/><path d="M18 16h1.5v3H18z"/><path d="M18.85 7.73c.62-.61 1-1.45 1-2.38C19.85 3.5 18.35 2 16.5 2v1.5c1.02 0 1.85.83 1.85 1.85S17.52 7.2 16.5 7.2v1.5c2.24 0 4 1.83 4 4.07V15H22v-2.24c0-2.22-1.28-4.14-3.15-5.03z"/><path d="M16.03 10.2H14.5c-1.02 0-1.85-.98-1.85-2s.83-1.75 1.85-1.75v-1.5a3.35 3.35 0 0 0 0 6.7h1.53c1.05 0 1.97.74 1.97 2.05V15h1.5v-1.64c0-1.81-1.6-3.16-3.47-3.16z"/>',spa:'<path d="M8.55 12c-1.07-.71-2.25-1.27-3.53-1.61 1.28.34 2.46.9 3.53 1.61zm10.43-1.61c-1.29.34-2.49.91-3.57 1.64 1.08-.73 2.28-1.3 3.57-1.64z"/> <path d="M15.49 9.63c-.18-2.79-1.31-5.51-3.43-7.63-2.14 2.14-3.32 4.86-3.55 7.63 1.28.68 2.46 1.56 3.49 2.63 1.03-1.06 2.21-1.94 3.49-2.63zm-6.5 2.65c-.14-.1-.3-.19-.45-.29.15.11.31.19.45.29zm6.42-.25c-.13.09-.27.16-.4.26.13-.1.27-.17.4-.26zM12 15.45C9.85 12.17 6.18 10 2 10c0 5.32 3.36 9.82 8.03 11.49.63.23 1.29.4 1.97.51.68-.12 1.33-.29 1.97-.51C18.64 19.82 22 15.32 22 10c-4.18 0-7.85 2.17-10 5.45z"/>',cake:'<path d="M12 6c1.11 0 2-.9 2-2 0-.38-.1-.73-.29-1.03L12 0l-1.71 2.97c-.19.3-.29.65-.29 1.03 0 1.1.9 2 2 2z"/><path d="M16.6 15.99l-1.07-1.07-1.08 1.07c-1.3 1.3-3.58 1.31-4.89 0l-1.07-1.07-1.09 1.07C6.75 16.64 5.88 17 4.96 17c-.73 0-1.4-.23-1.96-.61V21c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-4.61c-.56.38-1.23.61-1.96.61-.92 0-1.79-.36-2.44-1.01z"/><path d="M18 9h-5V7h-2v2H6c-1.66 0-3 1.34-3 3v1.54c0 1.08.88 1.96 1.96 1.96.52 0 1.02-.2 1.38-.57l2.14-2.13 2.13 2.13c.74.74 2.03.74 2.77 0l2.14-2.13 2.13 2.13c.37.37.86.57 1.38.57 1.08 0 1.96-.88 1.96-1.96V12C21 10.34 19.66 9 18 9z"/>',domain:'<path d="M20 19h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zM10 7H8V5h2v2zm0 4H8V9h2v2zm0 4H8v-2h2v2zm0 4H8v-2h2v2zM6 7H4V5h2v2zm0 4H4V9h2v2zm0 4H4v-2h2v2zm0 4H4v-2h2v2zm6-12V3H2v18h20V7H12z"/><path d="M18 11h-2v2h2v-2z"/><path d="M18 15h-2v2h2v-2z"/>',group:'<path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3z"/><path d="M8 11c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3z"/><path d="M8 13c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/><path d="M16 13c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>',group_add:'<path d="M8 10H5V7H3v3H0v2h3v3h2v-3h3v-2z"/><path d="M18 11c1.66 0 2.99-1.34 2.99-3S19.66 5 18 5c-.32 0-.63.05-.91.14.57.81.9 1.79.9 2.86 0 1.07-.34 2.04-.9 2.86.28.09.59.14.91.14z"/><path d="M13 11c1.66 0 2.99-1.34 2.99-3S14.66 5 13 5c-1.66 0-3 1.34-3 3s1.34 3 3 3z"/><path d="M19.62 13.16c.83.73 1.38 1.66 1.38 2.84v2h3v-2c0-1.54-2.37-2.49-4.38-2.84z"/><path d="M13 13c-2 0-6 1-6 3v2h12v-2c0-2-4-3-6-3z"/>',location_city:'<path d="M15 11V5l-3-3-3 3v2H3v14h18V11h-6zm-8 8H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm6 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm6 12h-2v-2h2v2zm0-4h-2v-2h2v2z"/>',mood:'<path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.01-18C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2z"/><path d="M15.5 11c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5z"/><path d="M8.5 11c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11z"/><path d="M12 17.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>',mood_bad:'<path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.01-18C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2z"/><path d="M15.5 11c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5z"/><path d="M8.5 11c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11z"/><path d="M12 14c-2.33 0-4.31 1.46-5.11 3.5h10.22c-.8-2.04-2.78-3.5-5.11-3.5z"/>',notifications:'<path d="M11.5 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2z"/><path d="M18 16v-5.5c0-3.07-2.13-5.64-5-6.32V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5v.68c-2.87.68-5 3.25-5 6.32V16l-2 2v1h17v-1l-2-2z"/>',notifications_none:'<path d="M11.5 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2z"/><path d="M16 17H7v-6.5C7 8.01 9.01 6 11.5 6S16 8.01 16 10.5V17zm2-1v-5.5c0-3.07-2.13-5.64-5-6.32V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5v.68c-2.87.68-5 3.25-5 6.32V16l-2 2v1h17v-1l-2-2z"/>',notifications_off:'<path d="M11.5 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2z"/><path d="M18 10.5c0-3.07-2.13-5.64-5-6.32V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5v.68c-.51.12-.99.32-1.45.56L18 14.18V10.5z"/><path d="M17.73 19l2 2L21 19.73 4.27 3 3 4.27l2.92 2.92C5.34 8.16 5 9.29 5 10.5V16l-2 2v1h14.73z"/>',notifications_active:'<path d="M6.58 3.58L5.15 2.15C2.76 3.97 1.18 6.8 1.03 10h2c.15-2.65 1.51-4.97 3.55-6.42z"/><path d="M19.97 10h2c-.15-3.2-1.73-6.03-4.13-7.85l-1.43 1.43c2.05 1.45 3.41 3.77 3.56 6.42z"/><path d="M18 10.5c0-3.07-2.13-5.64-5-6.32V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5v.68c-2.87.68-5 3.25-5 6.32V16l-2 2v1h17v-1l-2-2v-5.5z"/><path d="M11.5 22c.14 0 .27-.01.4-.04.65-.13 1.19-.58 1.44-1.18.1-.24.16-.5.16-.78h-4c0 1.1.9 2 2 2z"/>',notifications_paused:'<path d="M11.5 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2z"/><path d="M14 9.8l-2.8 3.4H14V15H9v-1.8l2.8-3.4H9V8h5v1.8zm4 6.2v-5.5c0-3.07-2.13-5.64-5-6.32V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5v.68c-2.87.68-5 3.25-5 6.32V16l-2 2v1h17v-1l-2-2z"/>',pages:'<path d="M3 5v6h5L7 7l4 1V3H5c-1.1 0-2 .9-2 2z"/><path d="M8 13H3v6c0 1.1.9 2 2 2h6v-5l-4 1 1-4z"/><path d="M17 17l-4-1v5h6c1.1 0 2-.9 2-2v-6h-5l1 4z"/><path d="M19 3h-6v5l4-1-1 4h5V5c0-1.1-.9-2-2-2z"/>',party_mode:'<path d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 3c1.63 0 3.06.79 3.98 2H12c-1.66 0-3 1.34-3 3 0 .35.07.69.18 1H7.1c-.06-.32-.1-.66-.1-1 0-2.76 2.24-5 5-5zm0 10c-1.63 0-3.06-.79-3.98-2H12c1.66 0 3-1.34 3-3 0-.35-.07-.69-.18-1h2.08c.07.32.1.66.1 1 0 2.76-2.24 5-5 5z"/>',people:'<path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3z"/><path d="M8 11c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3z"/><path d="M8 13c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/><path d="M16 13c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>',people_outline:'<path d="M21.5 17.5H14v-1.25c0-.46-.2-.86-.52-1.22.88-.3 1.96-.53 3.02-.53 2.44 0 5 1.21 5 1.75v1.25zm-9 0h-10v-1.25c0-.54 2.56-1.75 5-1.75s5 1.21 5 1.75v1.25zm4-4.5c-1.2 0-3.07.34-4.5 1-1.43-.67-3.3-1-4.5-1C5.33 13 1 14.08 1 16.25V19h22v-2.75c0-2.17-4.33-3.25-6.5-3.25z"/><path d="M7.5 6.5c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm0 5.5c1.93 0 3.5-1.57 3.5-3.5S9.43 5 7.5 5 4 6.57 4 8.5 5.57 12 7.5 12z"/><path d="M16.5 6.5c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm0 5.5c1.93 0 3.5-1.57 3.5-3.5S18.43 5 16.5 5 13 6.57 13 8.5s1.57 3.5 3.5 3.5z"/>',person:'<path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z"/><path d="M12 14c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>',person_add:'<path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z"/><path d="M6 10V7H4v3H1v2h3v3h2v-3h3v-2H6z"/><path d="M15 14c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>',person_outline:'<path d="M12 4C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 1.9c1.16 0 2.1.94 2.1 2.1 0 1.16-.94 2.1-2.1 2.1-1.16 0-2.1-.94-2.1-2.1 0-1.16.94-2.1 2.1-2.1"/><path d="M12 13c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4zm0 1.9c2.97 0 6.1 1.46 6.1 2.1v1.1H5.9V17c0-.64 3.13-2.1 6.1-2.1"/>',plus_one:'<path d="M10 8H8v4H4v2h4v4h2v-4h4v-2h-4z"/><path d="M14.5 6.08V7.9l2.5-.5V18h2V5z"/>',poll:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>',"public":'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>',school:'<path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z"/><path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>',sentiment_dissatisfied:'<circle cx="15.5" cy="9.5" r="1.5"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-6c-2.33 0-4.32 1.45-5.12 3.5h1.67c.69-1.19 1.97-2 3.45-2s2.75.81 3.45 2h1.67c-.8-2.05-2.79-3.5-5.12-3.5z"/>',sentiment_neutral:'<path d="M9 14h6v1.5H9z"/><circle cx="15.5" cy="9.5" r="1.5"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>',sentiment_satisfied:'<circle cx="15.5" cy="9.5" r="1.5"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-4c-1.48 0-2.75-.81-3.45-2H6.88c.8 2.05 2.79 3.5 5.12 3.5s4.32-1.45 5.12-3.5h-1.67c-.7 1.19-1.97 2-3.45 2z"/>',sentiment_very_dissatisfied:'<path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.01-18C6.47 2 2 6.47 2 12s4.47 10 9.99 10C17.51 22 22 17.53 22 12S17.52 2 11.99 2z"/><path d="M16.18 7.76l-1.06 1.06-1.06-1.06L13 8.82l1.06 1.06L13 10.94 14.06 12l1.06-1.06L16.18 12l1.06-1.06-1.06-1.06 1.06-1.06z"/><path d="M7.82 12l1.06-1.06L9.94 12 11 10.94 9.94 9.88 11 8.82 9.94 7.76 8.88 8.82 7.82 7.76 6.76 8.82l1.06 1.06-1.06 1.06z"/><path d="M12 14c-2.33 0-4.31 1.46-5.11 3.5h10.22c-.8-2.04-2.78-3.5-5.11-3.5z"/>',sentiment_very_satisfied:'<path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.01-18C6.47 2 2 6.47 2 12s4.47 10 9.99 10C17.51 22 22 17.53 22 12S17.52 2 11.99 2z"/><path d="M13 9.94L14.06 11l1.06-1.06L16.18 11l1.06-1.06-2.12-2.12z"/><path d="M8.88 9.94L9.94 11 11 9.94 8.88 7.82 6.76 9.94 7.82 11z"/><path d="M12 17.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>',share:'<path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>',whatshot:'<path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/>',check_box:'<path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>',check_box_outline_blank:'<path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>',indeterminate_check_box:'<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10H7v-2h10v2z"/>',radio_button_unchecked:'<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>',radio_button_checked:'<path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z"/><path d="M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>',star:'<path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>',star_half:'<path d="M22 9.74l-7.19-.62L12 2.5 9.19 9.13 2 9.74l5.46 4.73-1.64 7.03L12 17.77l6.18 3.73-1.63-7.03L22 9.74zM12 15.9V6.6l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.9z"/>',star_border:'<path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z"/>'}}function l(){return V}var H,V,s,e;return s=M(),e={},V={getShape:z,getShapes:t,getViewBox:d,getViewBoxes:p,setShape:h,setShapes:c,setViewBox:v,setViewBoxes:a,addShape:h,addShapes:c,addViewBox:v,addViewBoxes:a},H={$get:l,getShape:z,getShapes:t,getViewBox:d,getViewBoxes:p,setShape:h,setShapes:c,setViewBox:v,setViewBoxes:a,addShape:h,addShapes:c,addViewBox:v,addViewBoxes:a}});
'use strict';
angular.module("ngLocale", [], ["$provide", function($provide) {
var PLURAL_CATEGORY = {ZERO: "zero", ONE: "one", TWO: "two", FEW: "few", MANY: "many", OTHER: "other"};
$provide.value("$locale", {
  "DATETIME_FORMATS": {
    "AMPMS": [
      "AM",
      "PM"
    ],
    "DAY": [
      "domingo",
      "segunda-feira",
      "ter\u00e7a-feira",
      "quarta-feira",
      "quinta-feira",
      "sexta-feira",
      "s\u00e1bado"
    ],
    "ERANAMES": [
      "antes de Cristo",
      "depois de Cristo"
    ],
    "ERAS": [
      "a.C.",
      "d.C."
    ],
    "FIRSTDAYOFWEEK": 6,
    "MONTH": [
      "janeiro",
      "fevereiro",
      "mar\u00e7o",
      "abril",
      "maio",
      "junho",
      "julho",
      "agosto",
      "setembro",
      "outubro",
      "novembro",
      "dezembro"
    ],
    "SHORTDAY": [
      "dom",
      "seg",
      "ter",
      "qua",
      "qui",
      "sex",
      "s\u00e1b"
    ],
    "SHORTMONTH": [
      "jan",
      "fev",
      "mar",
      "abr",
      "mai",
      "jun",
      "jul",
      "ago",
      "set",
      "out",
      "nov",
      "dez"
    ],
    "STANDALONEMONTH": [
      "janeiro",
      "fevereiro",
      "mar\u00e7o",
      "abril",
      "maio",
      "junho",
      "julho",
      "agosto",
      "setembro",
      "outubro",
      "novembro",
      "dezembro"
    ],
    "WEEKENDRANGE": [
      5,
      6
    ],
    "fullDate": "EEEE, d 'de' MMMM 'de' y",
    "longDate": "d 'de' MMMM 'de' y",
    "medium": "d 'de' MMM 'de' y HH:mm:ss",
    "mediumDate": "d 'de' MMM 'de' y",
    "mediumTime": "HH:mm:ss",
    "short": "dd/MM/yy HH:mm",
    "shortDate": "dd/MM/yy",
    "shortTime": "HH:mm"
  },
  "NUMBER_FORMATS": {
    "CURRENCY_SYM": "R$",
    "DECIMAL_SEP": ",",
    "GROUP_SEP": ".",
    "PATTERNS": [
      {
        "gSize": 3,
        "lgSize": 3,
        "maxFrac": 3,
        "minFrac": 0,
        "minInt": 1,
        "negPre": "-",
        "negSuf": "",
        "posPre": "",
        "posSuf": ""
      },
      {
        "gSize": 3,
        "lgSize": 3,
        "maxFrac": 2,
        "minFrac": 2,
        "minInt": 1,
        "negPre": "-\u00a4",
        "negSuf": "",
        "posPre": "\u00a4",
        "posSuf": ""
      }
    ]
  },
  "id": "pt-br",
  "localeID": "pt_BR",
  "pluralCat": function(n, opt_precision) {  if (n >= 0 && n <= 2 && n != 2) {    return PLURAL_CATEGORY.ONE;  }  return PLURAL_CATEGORY.OTHER;}
});
}]);

//! moment.js
//! version : 2.10.6
//! authors : Tim Wood, Iskren Chernev, Moment.js contributors
//! license : MIT
//! momentjs.com

(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
    typeof define === 'function' && define.amd ? define(factory) :
    global.moment = factory()
}(this, function () { 'use strict';

    var hookCallback;

    function utils_hooks__hooks () {
        return hookCallback.apply(null, arguments);
    }

    // This is done to register the method called with moment()
    // without creating circular dependencies.
    function setHookCallback (callback) {
        hookCallback = callback;
    }

    function isArray(input) {
        return Object.prototype.toString.call(input) === '[object Array]';
    }

    function isDate(input) {
        return input instanceof Date || Object.prototype.toString.call(input) === '[object Date]';
    }

    function map(arr, fn) {
        var res = [], i;
        for (i = 0; i < arr.length; ++i) {
            res.push(fn(arr[i], i));
        }
        return res;
    }

    function hasOwnProp(a, b) {
        return Object.prototype.hasOwnProperty.call(a, b);
    }

    function extend(a, b) {
        for (var i in b) {
            if (hasOwnProp(b, i)) {
                a[i] = b[i];
            }
        }

        if (hasOwnProp(b, 'toString')) {
            a.toString = b.toString;
        }

        if (hasOwnProp(b, 'valueOf')) {
            a.valueOf = b.valueOf;
        }

        return a;
    }

    function create_utc__createUTC (input, format, locale, strict) {
        return createLocalOrUTC(input, format, locale, strict, true).utc();
    }

    function defaultParsingFlags() {
        // We need to deep clone this object.
        return {
            empty           : false,
            unusedTokens    : [],
            unusedInput     : [],
            overflow        : -2,
            charsLeftOver   : 0,
            nullInput       : false,
            invalidMonth    : null,
            invalidFormat   : false,
            userInvalidated : false,
            iso             : false
        };
    }

    function getParsingFlags(m) {
        if (m._pf == null) {
            m._pf = defaultParsingFlags();
        }
        return m._pf;
    }

    function valid__isValid(m) {
        if (m._isValid == null) {
            var flags = getParsingFlags(m);
            m._isValid = !isNaN(m._d.getTime()) &&
                flags.overflow < 0 &&
                !flags.empty &&
                !flags.invalidMonth &&
                !flags.invalidWeekday &&
                !flags.nullInput &&
                !flags.invalidFormat &&
                !flags.userInvalidated;

            if (m._strict) {
                m._isValid = m._isValid &&
                    flags.charsLeftOver === 0 &&
                    flags.unusedTokens.length === 0 &&
                    flags.bigHour === undefined;
            }
        }
        return m._isValid;
    }

    function valid__createInvalid (flags) {
        var m = create_utc__createUTC(NaN);
        if (flags != null) {
            extend(getParsingFlags(m), flags);
        }
        else {
            getParsingFlags(m).userInvalidated = true;
        }

        return m;
    }

    var momentProperties = utils_hooks__hooks.momentProperties = [];

    function copyConfig(to, from) {
        var i, prop, val;

        if (typeof from._isAMomentObject !== 'undefined') {
            to._isAMomentObject = from._isAMomentObject;
        }
        if (typeof from._i !== 'undefined') {
            to._i = from._i;
        }
        if (typeof from._f !== 'undefined') {
            to._f = from._f;
        }
        if (typeof from._l !== 'undefined') {
            to._l = from._l;
        }
        if (typeof from._strict !== 'undefined') {
            to._strict = from._strict;
        }
        if (typeof from._tzm !== 'undefined') {
            to._tzm = from._tzm;
        }
        if (typeof from._isUTC !== 'undefined') {
            to._isUTC = from._isUTC;
        }
        if (typeof from._offset !== 'undefined') {
            to._offset = from._offset;
        }
        if (typeof from._pf !== 'undefined') {
            to._pf = getParsingFlags(from);
        }
        if (typeof from._locale !== 'undefined') {
            to._locale = from._locale;
        }

        if (momentProperties.length > 0) {
            for (i in momentProperties) {
                prop = momentProperties[i];
                val = from[prop];
                if (typeof val !== 'undefined') {
                    to[prop] = val;
                }
            }
        }

        return to;
    }

    var updateInProgress = false;

    // Moment prototype object
    function Moment(config) {
        copyConfig(this, config);
        this._d = new Date(config._d != null ? config._d.getTime() : NaN);
        // Prevent infinite loop in case updateOffset creates new moment
        // objects.
        if (updateInProgress === false) {
            updateInProgress = true;
            utils_hooks__hooks.updateOffset(this);
            updateInProgress = false;
        }
    }

    function isMoment (obj) {
        return obj instanceof Moment || (obj != null && obj._isAMomentObject != null);
    }

    function absFloor (number) {
        if (number < 0) {
            return Math.ceil(number);
        } else {
            return Math.floor(number);
        }
    }

    function toInt(argumentForCoercion) {
        var coercedNumber = +argumentForCoercion,
            value = 0;

        if (coercedNumber !== 0 && isFinite(coercedNumber)) {
            value = absFloor(coercedNumber);
        }

        return value;
    }

    function compareArrays(array1, array2, dontConvert) {
        var len = Math.min(array1.length, array2.length),
            lengthDiff = Math.abs(array1.length - array2.length),
            diffs = 0,
            i;
        for (i = 0; i < len; i++) {
            if ((dontConvert && array1[i] !== array2[i]) ||
                (!dontConvert && toInt(array1[i]) !== toInt(array2[i]))) {
                diffs++;
            }
        }
        return diffs + lengthDiff;
    }

    function Locale() {
    }

    var locales = {};
    var globalLocale;

    function normalizeLocale(key) {
        return key ? key.toLowerCase().replace('_', '-') : key;
    }

    // pick the locale from the array
    // try ['en-au', 'en-gb'] as 'en-au', 'en-gb', 'en', as in move through the list trying each
    // substring from most specific to least, but move to the next array item if it's a more specific variant than the current root
    function chooseLocale(names) {
        var i = 0, j, next, locale, split;

        while (i < names.length) {
            split = normalizeLocale(names[i]).split('-');
            j = split.length;
            next = normalizeLocale(names[i + 1]);
            next = next ? next.split('-') : null;
            while (j > 0) {
                locale = loadLocale(split.slice(0, j).join('-'));
                if (locale) {
                    return locale;
                }
                if (next && next.length >= j && compareArrays(split, next, true) >= j - 1) {
                    //the next array item is better than a shallower substring of this one
                    break;
                }
                j--;
            }
            i++;
        }
        return null;
    }

    function loadLocale(name) {
        var oldLocale = null;
        // TODO: Find a better way to register and load all the locales in Node
        if (!locales[name] && typeof module !== 'undefined' &&
                module && module.exports) {
            try {
                oldLocale = globalLocale._abbr;
                require('./locale/' + name);
                // because defineLocale currently also sets the global locale, we
                // want to undo that for lazy loaded locales
                locale_locales__getSetGlobalLocale(oldLocale);
            } catch (e) { }
        }
        return locales[name];
    }

    // This function will load locale and then set the global locale.  If
    // no arguments are passed in, it will simply return the current global
    // locale key.
    function locale_locales__getSetGlobalLocale (key, values) {
        var data;
        if (key) {
            if (typeof values === 'undefined') {
                data = locale_locales__getLocale(key);
            }
            else {
                data = defineLocale(key, values);
            }

            if (data) {
                // moment.duration._locale = moment._locale = data;
                globalLocale = data;
            }
        }

        return globalLocale._abbr;
    }

    function defineLocale (name, values) {
        if (values !== null) {
            values.abbr = name;
            locales[name] = locales[name] || new Locale();
            locales[name].set(values);

            // backwards compat for now: also set the locale
            locale_locales__getSetGlobalLocale(name);

            return locales[name];
        } else {
            // useful for testing
            delete locales[name];
            return null;
        }
    }

    // returns locale data
    function locale_locales__getLocale (key) {
        var locale;

        if (key && key._locale && key._locale._abbr) {
            key = key._locale._abbr;
        }

        if (!key) {
            return globalLocale;
        }

        if (!isArray(key)) {
            //short-circuit everything else
            locale = loadLocale(key);
            if (locale) {
                return locale;
            }
            key = [key];
        }

        return chooseLocale(key);
    }

    var aliases = {};

    function addUnitAlias (unit, shorthand) {
        var lowerCase = unit.toLowerCase();
        aliases[lowerCase] = aliases[lowerCase + 's'] = aliases[shorthand] = unit;
    }

    function normalizeUnits(units) {
        return typeof units === 'string' ? aliases[units] || aliases[units.toLowerCase()] : undefined;
    }

    function normalizeObjectUnits(inputObject) {
        var normalizedInput = {},
            normalizedProp,
            prop;

        for (prop in inputObject) {
            if (hasOwnProp(inputObject, prop)) {
                normalizedProp = normalizeUnits(prop);
                if (normalizedProp) {
                    normalizedInput[normalizedProp] = inputObject[prop];
                }
            }
        }

        return normalizedInput;
    }

    function makeGetSet (unit, keepTime) {
        return function (value) {
            if (value != null) {
                get_set__set(this, unit, value);
                utils_hooks__hooks.updateOffset(this, keepTime);
                return this;
            } else {
                return get_set__get(this, unit);
            }
        };
    }

    function get_set__get (mom, unit) {
        return mom._d['get' + (mom._isUTC ? 'UTC' : '') + unit]();
    }

    function get_set__set (mom, unit, value) {
        return mom._d['set' + (mom._isUTC ? 'UTC' : '') + unit](value);
    }

    // MOMENTS

    function getSet (units, value) {
        var unit;
        if (typeof units === 'object') {
            for (unit in units) {
                this.set(unit, units[unit]);
            }
        } else {
            units = normalizeUnits(units);
            if (typeof this[units] === 'function') {
                return this[units](value);
            }
        }
        return this;
    }

    function zeroFill(number, targetLength, forceSign) {
        var absNumber = '' + Math.abs(number),
            zerosToFill = targetLength - absNumber.length,
            sign = number >= 0;
        return (sign ? (forceSign ? '+' : '') : '-') +
            Math.pow(10, Math.max(0, zerosToFill)).toString().substr(1) + absNumber;
    }

    var formattingTokens = /(\[[^\[]*\])|(\\)?(Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Q|YYYYYY|YYYYY|YYYY|YY|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g;

    var localFormattingTokens = /(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g;

    var formatFunctions = {};

    var formatTokenFunctions = {};

    // token:    'M'
    // padded:   ['MM', 2]
    // ordinal:  'Mo'
    // callback: function () { this.month() + 1 }
    function addFormatToken (token, padded, ordinal, callback) {
        var func = callback;
        if (typeof callback === 'string') {
            func = function () {
                return this[callback]();
            };
        }
        if (token) {
            formatTokenFunctions[token] = func;
        }
        if (padded) {
            formatTokenFunctions[padded[0]] = function () {
                return zeroFill(func.apply(this, arguments), padded[1], padded[2]);
            };
        }
        if (ordinal) {
            formatTokenFunctions[ordinal] = function () {
                return this.localeData().ordinal(func.apply(this, arguments), token);
            };
        }
    }

    function removeFormattingTokens(input) {
        if (input.match(/\[[\s\S]/)) {
            return input.replace(/^\[|\]$/g, '');
        }
        return input.replace(/\\/g, '');
    }

    function makeFormatFunction(format) {
        var array = format.match(formattingTokens), i, length;

        for (i = 0, length = array.length; i < length; i++) {
            if (formatTokenFunctions[array[i]]) {
                array[i] = formatTokenFunctions[array[i]];
            } else {
                array[i] = removeFormattingTokens(array[i]);
            }
        }

        return function (mom) {
            var output = '';
            for (i = 0; i < length; i++) {
                output += array[i] instanceof Function ? array[i].call(mom, format) : array[i];
            }
            return output;
        };
    }

    // format date using native date object
    function formatMoment(m, format) {
        if (!m.isValid()) {
            return m.localeData().invalidDate();
        }

        format = expandFormat(format, m.localeData());
        formatFunctions[format] = formatFunctions[format] || makeFormatFunction(format);

        return formatFunctions[format](m);
    }

    function expandFormat(format, locale) {
        var i = 5;

        function replaceLongDateFormatTokens(input) {
            return locale.longDateFormat(input) || input;
        }

        localFormattingTokens.lastIndex = 0;
        while (i >= 0 && localFormattingTokens.test(format)) {
            format = format.replace(localFormattingTokens, replaceLongDateFormatTokens);
            localFormattingTokens.lastIndex = 0;
            i -= 1;
        }

        return format;
    }

    var match1         = /\d/;            //       0 - 9
    var match2         = /\d\d/;          //      00 - 99
    var match3         = /\d{3}/;         //     000 - 999
    var match4         = /\d{4}/;         //    0000 - 9999
    var match6         = /[+-]?\d{6}/;    // -999999 - 999999
    var match1to2      = /\d\d?/;         //       0 - 99
    var match1to3      = /\d{1,3}/;       //       0 - 999
    var match1to4      = /\d{1,4}/;       //       0 - 9999
    var match1to6      = /[+-]?\d{1,6}/;  // -999999 - 999999

    var matchUnsigned  = /\d+/;           //       0 - inf
    var matchSigned    = /[+-]?\d+/;      //    -inf - inf

    var matchOffset    = /Z|[+-]\d\d:?\d\d/gi; // +00:00 -00:00 +0000 -0000 or Z

    var matchTimestamp = /[+-]?\d+(\.\d{1,3})?/; // 123456789 123456789.123

    // any word (or two) characters or numbers including two/three word month in arabic.
    var matchWord = /[0-9]*['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+|[\u0600-\u06FF\/]+(\s*?[\u0600-\u06FF]+){1,2}/i;

    var regexes = {};

    function isFunction (sth) {
        // https://github.com/moment/moment/issues/2325
        return typeof sth === 'function' &&
            Object.prototype.toString.call(sth) === '[object Function]';
    }


    function addRegexToken (token, regex, strictRegex) {
        regexes[token] = isFunction(regex) ? regex : function (isStrict) {
            return (isStrict && strictRegex) ? strictRegex : regex;
        };
    }

    function getParseRegexForToken (token, config) {
        if (!hasOwnProp(regexes, token)) {
            return new RegExp(unescapeFormat(token));
        }

        return regexes[token](config._strict, config._locale);
    }

    // Code from http://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript
    function unescapeFormat(s) {
        return s.replace('\\', '').replace(/\\(\[)|\\(\])|\[([^\]\[]*)\]|\\(.)/g, function (matched, p1, p2, p3, p4) {
            return p1 || p2 || p3 || p4;
        }).replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    }

    var tokens = {};

    function addParseToken (token, callback) {
        var i, func = callback;
        if (typeof token === 'string') {
            token = [token];
        }
        if (typeof callback === 'number') {
            func = function (input, array) {
                array[callback] = toInt(input);
            };
        }
        for (i = 0; i < token.length; i++) {
            tokens[token[i]] = func;
        }
    }

    function addWeekParseToken (token, callback) {
        addParseToken(token, function (input, array, config, token) {
            config._w = config._w || {};
            callback(input, config._w, config, token);
        });
    }

    function addTimeToArrayFromToken(token, input, config) {
        if (input != null && hasOwnProp(tokens, token)) {
            tokens[token](input, config._a, config, token);
        }
    }

    var YEAR = 0;
    var MONTH = 1;
    var DATE = 2;
    var HOUR = 3;
    var MINUTE = 4;
    var SECOND = 5;
    var MILLISECOND = 6;

    function daysInMonth(year, month) {
        return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    }

    // FORMATTING

    addFormatToken('M', ['MM', 2], 'Mo', function () {
        return this.month() + 1;
    });

    addFormatToken('MMM', 0, 0, function (format) {
        return this.localeData().monthsShort(this, format);
    });

    addFormatToken('MMMM', 0, 0, function (format) {
        return this.localeData().months(this, format);
    });

    // ALIASES

    addUnitAlias('month', 'M');

    // PARSING

    addRegexToken('M',    match1to2);
    addRegexToken('MM',   match1to2, match2);
    addRegexToken('MMM',  matchWord);
    addRegexToken('MMMM', matchWord);

    addParseToken(['M', 'MM'], function (input, array) {
        array[MONTH] = toInt(input) - 1;
    });

    addParseToken(['MMM', 'MMMM'], function (input, array, config, token) {
        var month = config._locale.monthsParse(input, token, config._strict);
        // if we didn't find a month name, mark the date as invalid.
        if (month != null) {
            array[MONTH] = month;
        } else {
            getParsingFlags(config).invalidMonth = input;
        }
    });

    // LOCALES

    var defaultLocaleMonths = 'January_February_March_April_May_June_July_August_September_October_November_December'.split('_');
    function localeMonths (m) {
        return this._months[m.month()];
    }

    var defaultLocaleMonthsShort = 'Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec'.split('_');
    function localeMonthsShort (m) {
        return this._monthsShort[m.month()];
    }

    function localeMonthsParse (monthName, format, strict) {
        var i, mom, regex;

        if (!this._monthsParse) {
            this._monthsParse = [];
            this._longMonthsParse = [];
            this._shortMonthsParse = [];
        }

        for (i = 0; i < 12; i++) {
            // make the regex if we don't have it already
            mom = create_utc__createUTC([2000, i]);
            if (strict && !this._longMonthsParse[i]) {
                this._longMonthsParse[i] = new RegExp('^' + this.months(mom, '').replace('.', '') + '$', 'i');
                this._shortMonthsParse[i] = new RegExp('^' + this.monthsShort(mom, '').replace('.', '') + '$', 'i');
            }
            if (!strict && !this._monthsParse[i]) {
                regex = '^' + this.months(mom, '') + '|^' + this.monthsShort(mom, '');
                this._monthsParse[i] = new RegExp(regex.replace('.', ''), 'i');
            }
            // test the regex
            if (strict && format === 'MMMM' && this._longMonthsParse[i].test(monthName)) {
                return i;
            } else if (strict && format === 'MMM' && this._shortMonthsParse[i].test(monthName)) {
                return i;
            } else if (!strict && this._monthsParse[i].test(monthName)) {
                return i;
            }
        }
    }

    // MOMENTS

    function setMonth (mom, value) {
        var dayOfMonth;

        // TODO: Move this out of here!
        if (typeof value === 'string') {
            value = mom.localeData().monthsParse(value);
            // TODO: Another silent failure?
            if (typeof value !== 'number') {
                return mom;
            }
        }

        dayOfMonth = Math.min(mom.date(), daysInMonth(mom.year(), value));
        mom._d['set' + (mom._isUTC ? 'UTC' : '') + 'Month'](value, dayOfMonth);
        return mom;
    }

    function getSetMonth (value) {
        if (value != null) {
            setMonth(this, value);
            utils_hooks__hooks.updateOffset(this, true);
            return this;
        } else {
            return get_set__get(this, 'Month');
        }
    }

    function getDaysInMonth () {
        return daysInMonth(this.year(), this.month());
    }

    function checkOverflow (m) {
        var overflow;
        var a = m._a;

        if (a && getParsingFlags(m).overflow === -2) {
            overflow =
                a[MONTH]       < 0 || a[MONTH]       > 11  ? MONTH :
                a[DATE]        < 1 || a[DATE]        > daysInMonth(a[YEAR], a[MONTH]) ? DATE :
                a[HOUR]        < 0 || a[HOUR]        > 24 || (a[HOUR] === 24 && (a[MINUTE] !== 0 || a[SECOND] !== 0 || a[MILLISECOND] !== 0)) ? HOUR :
                a[MINUTE]      < 0 || a[MINUTE]      > 59  ? MINUTE :
                a[SECOND]      < 0 || a[SECOND]      > 59  ? SECOND :
                a[MILLISECOND] < 0 || a[MILLISECOND] > 999 ? MILLISECOND :
                -1;

            if (getParsingFlags(m)._overflowDayOfYear && (overflow < YEAR || overflow > DATE)) {
                overflow = DATE;
            }

            getParsingFlags(m).overflow = overflow;
        }

        return m;
    }

    function warn(msg) {
        if (utils_hooks__hooks.suppressDeprecationWarnings === false && typeof console !== 'undefined' && console.warn) {
            console.warn('Deprecation warning: ' + msg);
        }
    }

    function deprecate(msg, fn) {
        var firstTime = true;

        return extend(function () {
            if (firstTime) {
                warn(msg + '\n' + (new Error()).stack);
                firstTime = false;
            }
            return fn.apply(this, arguments);
        }, fn);
    }

    var deprecations = {};

    function deprecateSimple(name, msg) {
        if (!deprecations[name]) {
            warn(msg);
            deprecations[name] = true;
        }
    }

    utils_hooks__hooks.suppressDeprecationWarnings = false;

    var from_string__isoRegex = /^\s*(?:[+-]\d{6}|\d{4})-(?:(\d\d-\d\d)|(W\d\d$)|(W\d\d-\d)|(\d\d\d))((T| )(\d\d(:\d\d(:\d\d(\.\d+)?)?)?)?([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?$/;

    var isoDates = [
        ['YYYYYY-MM-DD', /[+-]\d{6}-\d{2}-\d{2}/],
        ['YYYY-MM-DD', /\d{4}-\d{2}-\d{2}/],
        ['GGGG-[W]WW-E', /\d{4}-W\d{2}-\d/],
        ['GGGG-[W]WW', /\d{4}-W\d{2}/],
        ['YYYY-DDD', /\d{4}-\d{3}/]
    ];

    // iso time formats and regexes
    var isoTimes = [
        ['HH:mm:ss.SSSS', /(T| )\d\d:\d\d:\d\d\.\d+/],
        ['HH:mm:ss', /(T| )\d\d:\d\d:\d\d/],
        ['HH:mm', /(T| )\d\d:\d\d/],
        ['HH', /(T| )\d\d/]
    ];

    var aspNetJsonRegex = /^\/?Date\((\-?\d+)/i;

    // date from iso format
    function configFromISO(config) {
        var i, l,
            string = config._i,
            match = from_string__isoRegex.exec(string);

        if (match) {
            getParsingFlags(config).iso = true;
            for (i = 0, l = isoDates.length; i < l; i++) {
                if (isoDates[i][1].exec(string)) {
                    config._f = isoDates[i][0];
                    break;
                }
            }
            for (i = 0, l = isoTimes.length; i < l; i++) {
                if (isoTimes[i][1].exec(string)) {
                    // match[6] should be 'T' or space
                    config._f += (match[6] || ' ') + isoTimes[i][0];
                    break;
                }
            }
            if (string.match(matchOffset)) {
                config._f += 'Z';
            }
            configFromStringAndFormat(config);
        } else {
            config._isValid = false;
        }
    }

    // date from iso format or fallback
    function configFromString(config) {
        var matched = aspNetJsonRegex.exec(config._i);

        if (matched !== null) {
            config._d = new Date(+matched[1]);
            return;
        }

        configFromISO(config);
        if (config._isValid === false) {
            delete config._isValid;
            utils_hooks__hooks.createFromInputFallback(config);
        }
    }

    utils_hooks__hooks.createFromInputFallback = deprecate(
        'moment construction falls back to js Date. This is ' +
        'discouraged and will be removed in upcoming major ' +
        'release. Please refer to ' +
        'https://github.com/moment/moment/issues/1407 for more info.',
        function (config) {
            config._d = new Date(config._i + (config._useUTC ? ' UTC' : ''));
        }
    );

    function createDate (y, m, d, h, M, s, ms) {
        //can't just apply() to create a date:
        //http://stackoverflow.com/questions/181348/instantiating-a-javascript-object-by-calling-prototype-constructor-apply
        var date = new Date(y, m, d, h, M, s, ms);

        //the date constructor doesn't accept years < 1970
        if (y < 1970) {
            date.setFullYear(y);
        }
        return date;
    }

    function createUTCDate (y) {
        var date = new Date(Date.UTC.apply(null, arguments));
        if (y < 1970) {
            date.setUTCFullYear(y);
        }
        return date;
    }

    addFormatToken(0, ['YY', 2], 0, function () {
        return this.year() % 100;
    });

    addFormatToken(0, ['YYYY',   4],       0, 'year');
    addFormatToken(0, ['YYYYY',  5],       0, 'year');
    addFormatToken(0, ['YYYYYY', 6, true], 0, 'year');

    // ALIASES

    addUnitAlias('year', 'y');

    // PARSING

    addRegexToken('Y',      matchSigned);
    addRegexToken('YY',     match1to2, match2);
    addRegexToken('YYYY',   match1to4, match4);
    addRegexToken('YYYYY',  match1to6, match6);
    addRegexToken('YYYYYY', match1to6, match6);

    addParseToken(['YYYYY', 'YYYYYY'], YEAR);
    addParseToken('YYYY', function (input, array) {
        array[YEAR] = input.length === 2 ? utils_hooks__hooks.parseTwoDigitYear(input) : toInt(input);
    });
    addParseToken('YY', function (input, array) {
        array[YEAR] = utils_hooks__hooks.parseTwoDigitYear(input);
    });

    // HELPERS

    function daysInYear(year) {
        return isLeapYear(year) ? 366 : 365;
    }

    function isLeapYear(year) {
        return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    }

    // HOOKS

    utils_hooks__hooks.parseTwoDigitYear = function (input) {
        return toInt(input) + (toInt(input) > 68 ? 1900 : 2000);
    };

    // MOMENTS

    var getSetYear = makeGetSet('FullYear', false);

    function getIsLeapYear () {
        return isLeapYear(this.year());
    }

    addFormatToken('w', ['ww', 2], 'wo', 'week');
    addFormatToken('W', ['WW', 2], 'Wo', 'isoWeek');

    // ALIASES

    addUnitAlias('week', 'w');
    addUnitAlias('isoWeek', 'W');

    // PARSING

    addRegexToken('w',  match1to2);
    addRegexToken('ww', match1to2, match2);
    addRegexToken('W',  match1to2);
    addRegexToken('WW', match1to2, match2);

    addWeekParseToken(['w', 'ww', 'W', 'WW'], function (input, week, config, token) {
        week[token.substr(0, 1)] = toInt(input);
    });

    // HELPERS

    // firstDayOfWeek       0 = sun, 6 = sat
    //                      the day of the week that starts the week
    //                      (usually sunday or monday)
    // firstDayOfWeekOfYear 0 = sun, 6 = sat
    //                      the first week is the week that contains the first
    //                      of this day of the week
    //                      (eg. ISO weeks use thursday (4))
    function weekOfYear(mom, firstDayOfWeek, firstDayOfWeekOfYear) {
        var end = firstDayOfWeekOfYear - firstDayOfWeek,
            daysToDayOfWeek = firstDayOfWeekOfYear - mom.day(),
            adjustedMoment;


        if (daysToDayOfWeek > end) {
            daysToDayOfWeek -= 7;
        }

        if (daysToDayOfWeek < end - 7) {
            daysToDayOfWeek += 7;
        }

        adjustedMoment = local__createLocal(mom).add(daysToDayOfWeek, 'd');
        return {
            week: Math.ceil(adjustedMoment.dayOfYear() / 7),
            year: adjustedMoment.year()
        };
    }

    // LOCALES

    function localeWeek (mom) {
        return weekOfYear(mom, this._week.dow, this._week.doy).week;
    }

    var defaultLocaleWeek = {
        dow : 0, // Sunday is the first day of the week.
        doy : 6  // The week that contains Jan 1st is the first week of the year.
    };

    function localeFirstDayOfWeek () {
        return this._week.dow;
    }

    function localeFirstDayOfYear () {
        return this._week.doy;
    }

    // MOMENTS

    function getSetWeek (input) {
        var week = this.localeData().week(this);
        return input == null ? week : this.add((input - week) * 7, 'd');
    }

    function getSetISOWeek (input) {
        var week = weekOfYear(this, 1, 4).week;
        return input == null ? week : this.add((input - week) * 7, 'd');
    }

    addFormatToken('DDD', ['DDDD', 3], 'DDDo', 'dayOfYear');

    // ALIASES

    addUnitAlias('dayOfYear', 'DDD');

    // PARSING

    addRegexToken('DDD',  match1to3);
    addRegexToken('DDDD', match3);
    addParseToken(['DDD', 'DDDD'], function (input, array, config) {
        config._dayOfYear = toInt(input);
    });

    // HELPERS

    //http://en.wikipedia.org/wiki/ISO_week_date#Calculating_a_date_given_the_year.2C_week_number_and_weekday
    function dayOfYearFromWeeks(year, week, weekday, firstDayOfWeekOfYear, firstDayOfWeek) {
        var week1Jan = 6 + firstDayOfWeek - firstDayOfWeekOfYear, janX = createUTCDate(year, 0, 1 + week1Jan), d = janX.getUTCDay(), dayOfYear;
        if (d < firstDayOfWeek) {
            d += 7;
        }

        weekday = weekday != null ? 1 * weekday : firstDayOfWeek;

        dayOfYear = 1 + week1Jan + 7 * (week - 1) - d + weekday;

        return {
            year: dayOfYear > 0 ? year : year - 1,
            dayOfYear: dayOfYear > 0 ?  dayOfYear : daysInYear(year - 1) + dayOfYear
        };
    }

    // MOMENTS

    function getSetDayOfYear (input) {
        var dayOfYear = Math.round((this.clone().startOf('day') - this.clone().startOf('year')) / 864e5) + 1;
        return input == null ? dayOfYear : this.add((input - dayOfYear), 'd');
    }

    // Pick the first defined of two or three arguments.
    function defaults(a, b, c) {
        if (a != null) {
            return a;
        }
        if (b != null) {
            return b;
        }
        return c;
    }

    function currentDateArray(config) {
        var now = new Date();
        if (config._useUTC) {
            return [now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()];
        }
        return [now.getFullYear(), now.getMonth(), now.getDate()];
    }

    // convert an array to a date.
    // the array should mirror the parameters below
    // note: all values past the year are optional and will default to the lowest possible value.
    // [year, month, day , hour, minute, second, millisecond]
    function configFromArray (config) {
        var i, date, input = [], currentDate, yearToUse;

        if (config._d) {
            return;
        }

        currentDate = currentDateArray(config);

        //compute day of the year from weeks and weekdays
        if (config._w && config._a[DATE] == null && config._a[MONTH] == null) {
            dayOfYearFromWeekInfo(config);
        }

        //if the day of the year is set, figure out what it is
        if (config._dayOfYear) {
            yearToUse = defaults(config._a[YEAR], currentDate[YEAR]);

            if (config._dayOfYear > daysInYear(yearToUse)) {
                getParsingFlags(config)._overflowDayOfYear = true;
            }

            date = createUTCDate(yearToUse, 0, config._dayOfYear);
            config._a[MONTH] = date.getUTCMonth();
            config._a[DATE] = date.getUTCDate();
        }

        // Default to current date.
        // * if no year, month, day of month are given, default to today
        // * if day of month is given, default month and year
        // * if month is given, default only year
        // * if year is given, don't default anything
        for (i = 0; i < 3 && config._a[i] == null; ++i) {
            config._a[i] = input[i] = currentDate[i];
        }

        // Zero out whatever was not defaulted, including time
        for (; i < 7; i++) {
            config._a[i] = input[i] = (config._a[i] == null) ? (i === 2 ? 1 : 0) : config._a[i];
        }

        // Check for 24:00:00.000
        if (config._a[HOUR] === 24 &&
                config._a[MINUTE] === 0 &&
                config._a[SECOND] === 0 &&
                config._a[MILLISECOND] === 0) {
            config._nextDay = true;
            config._a[HOUR] = 0;
        }

        config._d = (config._useUTC ? createUTCDate : createDate).apply(null, input);
        // Apply timezone offset from input. The actual utcOffset can be changed
        // with parseZone.
        if (config._tzm != null) {
            config._d.setUTCMinutes(config._d.getUTCMinutes() - config._tzm);
        }

        if (config._nextDay) {
            config._a[HOUR] = 24;
        }
    }

    function dayOfYearFromWeekInfo(config) {
        var w, weekYear, week, weekday, dow, doy, temp;

        w = config._w;
        if (w.GG != null || w.W != null || w.E != null) {
            dow = 1;
            doy = 4;

            // TODO: We need to take the current isoWeekYear, but that depends on
            // how we interpret now (local, utc, fixed offset). So create
            // a now version of current config (take local/utc/offset flags, and
            // create now).
            weekYear = defaults(w.GG, config._a[YEAR], weekOfYear(local__createLocal(), 1, 4).year);
            week = defaults(w.W, 1);
            weekday = defaults(w.E, 1);
        } else {
            dow = config._locale._week.dow;
            doy = config._locale._week.doy;

            weekYear = defaults(w.gg, config._a[YEAR], weekOfYear(local__createLocal(), dow, doy).year);
            week = defaults(w.w, 1);

            if (w.d != null) {
                // weekday -- low day numbers are considered next week
                weekday = w.d;
                if (weekday < dow) {
                    ++week;
                }
            } else if (w.e != null) {
                // local weekday -- counting starts from begining of week
                weekday = w.e + dow;
            } else {
                // default to begining of week
                weekday = dow;
            }
        }
        temp = dayOfYearFromWeeks(weekYear, week, weekday, doy, dow);

        config._a[YEAR] = temp.year;
        config._dayOfYear = temp.dayOfYear;
    }

    utils_hooks__hooks.ISO_8601 = function () {};

    // date from string and format string
    function configFromStringAndFormat(config) {
        // TODO: Move this to another part of the creation flow to prevent circular deps
        if (config._f === utils_hooks__hooks.ISO_8601) {
            configFromISO(config);
            return;
        }

        config._a = [];
        getParsingFlags(config).empty = true;

        // This array is used to make a Date, either with `new Date` or `Date.UTC`
        var string = '' + config._i,
            i, parsedInput, tokens, token, skipped,
            stringLength = string.length,
            totalParsedInputLength = 0;

        tokens = expandFormat(config._f, config._locale).match(formattingTokens) || [];

        for (i = 0; i < tokens.length; i++) {
            token = tokens[i];
            parsedInput = (string.match(getParseRegexForToken(token, config)) || [])[0];
            if (parsedInput) {
                skipped = string.substr(0, string.indexOf(parsedInput));
                if (skipped.length > 0) {
                    getParsingFlags(config).unusedInput.push(skipped);
                }
                string = string.slice(string.indexOf(parsedInput) + parsedInput.length);
                totalParsedInputLength += parsedInput.length;
            }
            // don't parse if it's not a known token
            if (formatTokenFunctions[token]) {
                if (parsedInput) {
                    getParsingFlags(config).empty = false;
                }
                else {
                    getParsingFlags(config).unusedTokens.push(token);
                }
                addTimeToArrayFromToken(token, parsedInput, config);
            }
            else if (config._strict && !parsedInput) {
                getParsingFlags(config).unusedTokens.push(token);
            }
        }

        // add remaining unparsed input length to the string
        getParsingFlags(config).charsLeftOver = stringLength - totalParsedInputLength;
        if (string.length > 0) {
            getParsingFlags(config).unusedInput.push(string);
        }

        // clear _12h flag if hour is <= 12
        if (getParsingFlags(config).bigHour === true &&
                config._a[HOUR] <= 12 &&
                config._a[HOUR] > 0) {
            getParsingFlags(config).bigHour = undefined;
        }
        // handle meridiem
        config._a[HOUR] = meridiemFixWrap(config._locale, config._a[HOUR], config._meridiem);

        configFromArray(config);
        checkOverflow(config);
    }


    function meridiemFixWrap (locale, hour, meridiem) {
        var isPm;

        if (meridiem == null) {
            // nothing to do
            return hour;
        }
        if (locale.meridiemHour != null) {
            return locale.meridiemHour(hour, meridiem);
        } else if (locale.isPM != null) {
            // Fallback
            isPm = locale.isPM(meridiem);
            if (isPm && hour < 12) {
                hour += 12;
            }
            if (!isPm && hour === 12) {
                hour = 0;
            }
            return hour;
        } else {
            // this is not supposed to happen
            return hour;
        }
    }

    function configFromStringAndArray(config) {
        var tempConfig,
            bestMoment,

            scoreToBeat,
            i,
            currentScore;

        if (config._f.length === 0) {
            getParsingFlags(config).invalidFormat = true;
            config._d = new Date(NaN);
            return;
        }

        for (i = 0; i < config._f.length; i++) {
            currentScore = 0;
            tempConfig = copyConfig({}, config);
            if (config._useUTC != null) {
                tempConfig._useUTC = config._useUTC;
            }
            tempConfig._f = config._f[i];
            configFromStringAndFormat(tempConfig);

            if (!valid__isValid(tempConfig)) {
                continue;
            }

            // if there is any input that was not parsed add a penalty for that format
            currentScore += getParsingFlags(tempConfig).charsLeftOver;

            //or tokens
            currentScore += getParsingFlags(tempConfig).unusedTokens.length * 10;

            getParsingFlags(tempConfig).score = currentScore;

            if (scoreToBeat == null || currentScore < scoreToBeat) {
                scoreToBeat = currentScore;
                bestMoment = tempConfig;
            }
        }

        extend(config, bestMoment || tempConfig);
    }

    function configFromObject(config) {
        if (config._d) {
            return;
        }

        var i = normalizeObjectUnits(config._i);
        config._a = [i.year, i.month, i.day || i.date, i.hour, i.minute, i.second, i.millisecond];

        configFromArray(config);
    }

    function createFromConfig (config) {
        var res = new Moment(checkOverflow(prepareConfig(config)));
        if (res._nextDay) {
            // Adding is smart enough around DST
            res.add(1, 'd');
            res._nextDay = undefined;
        }

        return res;
    }

    function prepareConfig (config) {
        var input = config._i,
            format = config._f;

        config._locale = config._locale || locale_locales__getLocale(config._l);

        if (input === null || (format === undefined && input === '')) {
            return valid__createInvalid({nullInput: true});
        }

        if (typeof input === 'string') {
            config._i = input = config._locale.preparse(input);
        }

        if (isMoment(input)) {
            return new Moment(checkOverflow(input));
        } else if (isArray(format)) {
            configFromStringAndArray(config);
        } else if (format) {
            configFromStringAndFormat(config);
        } else if (isDate(input)) {
            config._d = input;
        } else {
            configFromInput(config);
        }

        return config;
    }

    function configFromInput(config) {
        var input = config._i;
        if (input === undefined) {
            config._d = new Date();
        } else if (isDate(input)) {
            config._d = new Date(+input);
        } else if (typeof input === 'string') {
            configFromString(config);
        } else if (isArray(input)) {
            config._a = map(input.slice(0), function (obj) {
                return parseInt(obj, 10);
            });
            configFromArray(config);
        } else if (typeof(input) === 'object') {
            configFromObject(config);
        } else if (typeof(input) === 'number') {
            // from milliseconds
            config._d = new Date(input);
        } else {
            utils_hooks__hooks.createFromInputFallback(config);
        }
    }

    function createLocalOrUTC (input, format, locale, strict, isUTC) {
        var c = {};

        if (typeof(locale) === 'boolean') {
            strict = locale;
            locale = undefined;
        }
        // object construction must be done this way.
        // https://github.com/moment/moment/issues/1423
        c._isAMomentObject = true;
        c._useUTC = c._isUTC = isUTC;
        c._l = locale;
        c._i = input;
        c._f = format;
        c._strict = strict;

        return createFromConfig(c);
    }

    function local__createLocal (input, format, locale, strict) {
        return createLocalOrUTC(input, format, locale, strict, false);
    }

    var prototypeMin = deprecate(
         'moment().min is deprecated, use moment.min instead. https://github.com/moment/moment/issues/1548',
         function () {
             var other = local__createLocal.apply(null, arguments);
             return other < this ? this : other;
         }
     );

    var prototypeMax = deprecate(
        'moment().max is deprecated, use moment.max instead. https://github.com/moment/moment/issues/1548',
        function () {
            var other = local__createLocal.apply(null, arguments);
            return other > this ? this : other;
        }
    );

    // Pick a moment m from moments so that m[fn](other) is true for all
    // other. This relies on the function fn to be transitive.
    //
    // moments should either be an array of moment objects or an array, whose
    // first element is an array of moment objects.
    function pickBy(fn, moments) {
        var res, i;
        if (moments.length === 1 && isArray(moments[0])) {
            moments = moments[0];
        }
        if (!moments.length) {
            return local__createLocal();
        }
        res = moments[0];
        for (i = 1; i < moments.length; ++i) {
            if (!moments[i].isValid() || moments[i][fn](res)) {
                res = moments[i];
            }
        }
        return res;
    }

    // TODO: Use [].sort instead?
    function min () {
        var args = [].slice.call(arguments, 0);

        return pickBy('isBefore', args);
    }

    function max () {
        var args = [].slice.call(arguments, 0);

        return pickBy('isAfter', args);
    }

    function Duration (duration) {
        var normalizedInput = normalizeObjectUnits(duration),
            years = normalizedInput.year || 0,
            quarters = normalizedInput.quarter || 0,
            months = normalizedInput.month || 0,
            weeks = normalizedInput.week || 0,
            days = normalizedInput.day || 0,
            hours = normalizedInput.hour || 0,
            minutes = normalizedInput.minute || 0,
            seconds = normalizedInput.second || 0,
            milliseconds = normalizedInput.millisecond || 0;

        // representation for dateAddRemove
        this._milliseconds = +milliseconds +
            seconds * 1e3 + // 1000
            minutes * 6e4 + // 1000 * 60
            hours * 36e5; // 1000 * 60 * 60
        // Because of dateAddRemove treats 24 hours as different from a
        // day when working around DST, we need to store them separately
        this._days = +days +
            weeks * 7;
        // It is impossible translate months into days without knowing
        // which months you are are talking about, so we have to store
        // it separately.
        this._months = +months +
            quarters * 3 +
            years * 12;

        this._data = {};

        this._locale = locale_locales__getLocale();

        this._bubble();
    }

    function isDuration (obj) {
        return obj instanceof Duration;
    }

    function offset (token, separator) {
        addFormatToken(token, 0, 0, function () {
            var offset = this.utcOffset();
            var sign = '+';
            if (offset < 0) {
                offset = -offset;
                sign = '-';
            }
            return sign + zeroFill(~~(offset / 60), 2) + separator + zeroFill(~~(offset) % 60, 2);
        });
    }

    offset('Z', ':');
    offset('ZZ', '');

    // PARSING

    addRegexToken('Z',  matchOffset);
    addRegexToken('ZZ', matchOffset);
    addParseToken(['Z', 'ZZ'], function (input, array, config) {
        config._useUTC = true;
        config._tzm = offsetFromString(input);
    });

    // HELPERS

    // timezone chunker
    // '+10:00' > ['10',  '00']
    // '-1530'  > ['-15', '30']
    var chunkOffset = /([\+\-]|\d\d)/gi;

    function offsetFromString(string) {
        var matches = ((string || '').match(matchOffset) || []);
        var chunk   = matches[matches.length - 1] || [];
        var parts   = (chunk + '').match(chunkOffset) || ['-', 0, 0];
        var minutes = +(parts[1] * 60) + toInt(parts[2]);

        return parts[0] === '+' ? minutes : -minutes;
    }

    // Return a moment from input, that is local/utc/zone equivalent to model.
    function cloneWithOffset(input, model) {
        var res, diff;
        if (model._isUTC) {
            res = model.clone();
            diff = (isMoment(input) || isDate(input) ? +input : +local__createLocal(input)) - (+res);
            // Use low-level api, because this fn is low-level api.
            res._d.setTime(+res._d + diff);
            utils_hooks__hooks.updateOffset(res, false);
            return res;
        } else {
            return local__createLocal(input).local();
        }
    }

    function getDateOffset (m) {
        // On Firefox.24 Date#getTimezoneOffset returns a floating point.
        // https://github.com/moment/moment/pull/1871
        return -Math.round(m._d.getTimezoneOffset() / 15) * 15;
    }

    // HOOKS

    // This function will be called whenever a moment is mutated.
    // It is intended to keep the offset in sync with the timezone.
    utils_hooks__hooks.updateOffset = function () {};

    // MOMENTS

    // keepLocalTime = true means only change the timezone, without
    // affecting the local hour. So 5:31:26 +0300 --[utcOffset(2, true)]-->
    // 5:31:26 +0200 It is possible that 5:31:26 doesn't exist with offset
    // +0200, so we adjust the time as needed, to be valid.
    //
    // Keeping the time actually adds/subtracts (one hour)
    // from the actual represented time. That is why we call updateOffset
    // a second time. In case it wants us to change the offset again
    // _changeInProgress == true case, then we have to adjust, because
    // there is no such time in the given timezone.
    function getSetOffset (input, keepLocalTime) {
        var offset = this._offset || 0,
            localAdjust;
        if (input != null) {
            if (typeof input === 'string') {
                input = offsetFromString(input);
            }
            if (Math.abs(input) < 16) {
                input = input * 60;
            }
            if (!this._isUTC && keepLocalTime) {
                localAdjust = getDateOffset(this);
            }
            this._offset = input;
            this._isUTC = true;
            if (localAdjust != null) {
                this.add(localAdjust, 'm');
            }
            if (offset !== input) {
                if (!keepLocalTime || this._changeInProgress) {
                    add_subtract__addSubtract(this, create__createDuration(input - offset, 'm'), 1, false);
                } else if (!this._changeInProgress) {
                    this._changeInProgress = true;
                    utils_hooks__hooks.updateOffset(this, true);
                    this._changeInProgress = null;
                }
            }
            return this;
        } else {
            return this._isUTC ? offset : getDateOffset(this);
        }
    }

    function getSetZone (input, keepLocalTime) {
        if (input != null) {
            if (typeof input !== 'string') {
                input = -input;
            }

            this.utcOffset(input, keepLocalTime);

            return this;
        } else {
            return -this.utcOffset();
        }
    }

    function setOffsetToUTC (keepLocalTime) {
        return this.utcOffset(0, keepLocalTime);
    }

    function setOffsetToLocal (keepLocalTime) {
        if (this._isUTC) {
            this.utcOffset(0, keepLocalTime);
            this._isUTC = false;

            if (keepLocalTime) {
                this.subtract(getDateOffset(this), 'm');
            }
        }
        return this;
    }

    function setOffsetToParsedOffset () {
        if (this._tzm) {
            this.utcOffset(this._tzm);
        } else if (typeof this._i === 'string') {
            this.utcOffset(offsetFromString(this._i));
        }
        return this;
    }

    function hasAlignedHourOffset (input) {
        input = input ? local__createLocal(input).utcOffset() : 0;

        return (this.utcOffset() - input) % 60 === 0;
    }

    function isDaylightSavingTime () {
        return (
            this.utcOffset() > this.clone().month(0).utcOffset() ||
            this.utcOffset() > this.clone().month(5).utcOffset()
        );
    }

    function isDaylightSavingTimeShifted () {
        if (typeof this._isDSTShifted !== 'undefined') {
            return this._isDSTShifted;
        }

        var c = {};

        copyConfig(c, this);
        c = prepareConfig(c);

        if (c._a) {
            var other = c._isUTC ? create_utc__createUTC(c._a) : local__createLocal(c._a);
            this._isDSTShifted = this.isValid() &&
                compareArrays(c._a, other.toArray()) > 0;
        } else {
            this._isDSTShifted = false;
        }

        return this._isDSTShifted;
    }

    function isLocal () {
        return !this._isUTC;
    }

    function isUtcOffset () {
        return this._isUTC;
    }

    function isUtc () {
        return this._isUTC && this._offset === 0;
    }

    var aspNetRegex = /(\-)?(?:(\d*)\.)?(\d+)\:(\d+)(?:\:(\d+)\.?(\d{3})?)?/;

    // from http://docs.closure-library.googlecode.com/git/closure_goog_date_date.js.source.html
    // somewhat more in line with 4.4.3.2 2004 spec, but allows decimal anywhere
    var create__isoRegex = /^(-)?P(?:(?:([0-9,.]*)Y)?(?:([0-9,.]*)M)?(?:([0-9,.]*)D)?(?:T(?:([0-9,.]*)H)?(?:([0-9,.]*)M)?(?:([0-9,.]*)S)?)?|([0-9,.]*)W)$/;

    function create__createDuration (input, key) {
        var duration = input,
            // matching against regexp is expensive, do it on demand
            match = null,
            sign,
            ret,
            diffRes;

        if (isDuration(input)) {
            duration = {
                ms : input._milliseconds,
                d  : input._days,
                M  : input._months
            };
        } else if (typeof input === 'number') {
            duration = {};
            if (key) {
                duration[key] = input;
            } else {
                duration.milliseconds = input;
            }
        } else if (!!(match = aspNetRegex.exec(input))) {
            sign = (match[1] === '-') ? -1 : 1;
            duration = {
                y  : 0,
                d  : toInt(match[DATE])        * sign,
                h  : toInt(match[HOUR])        * sign,
                m  : toInt(match[MINUTE])      * sign,
                s  : toInt(match[SECOND])      * sign,
                ms : toInt(match[MILLISECOND]) * sign
            };
        } else if (!!(match = create__isoRegex.exec(input))) {
            sign = (match[1] === '-') ? -1 : 1;
            duration = {
                y : parseIso(match[2], sign),
                M : parseIso(match[3], sign),
                d : parseIso(match[4], sign),
                h : parseIso(match[5], sign),
                m : parseIso(match[6], sign),
                s : parseIso(match[7], sign),
                w : parseIso(match[8], sign)
            };
        } else if (duration == null) {// checks for null or undefined
            duration = {};
        } else if (typeof duration === 'object' && ('from' in duration || 'to' in duration)) {
            diffRes = momentsDifference(local__createLocal(duration.from), local__createLocal(duration.to));

            duration = {};
            duration.ms = diffRes.milliseconds;
            duration.M = diffRes.months;
        }

        ret = new Duration(duration);

        if (isDuration(input) && hasOwnProp(input, '_locale')) {
            ret._locale = input._locale;
        }

        return ret;
    }

    create__createDuration.fn = Duration.prototype;

    function parseIso (inp, sign) {
        // We'd normally use ~~inp for this, but unfortunately it also
        // converts floats to ints.
        // inp may be undefined, so careful calling replace on it.
        var res = inp && parseFloat(inp.replace(',', '.'));
        // apply sign while we're at it
        return (isNaN(res) ? 0 : res) * sign;
    }

    function positiveMomentsDifference(base, other) {
        var res = {milliseconds: 0, months: 0};

        res.months = other.month() - base.month() +
            (other.year() - base.year()) * 12;
        if (base.clone().add(res.months, 'M').isAfter(other)) {
            --res.months;
        }

        res.milliseconds = +other - +(base.clone().add(res.months, 'M'));

        return res;
    }

    function momentsDifference(base, other) {
        var res;
        other = cloneWithOffset(other, base);
        if (base.isBefore(other)) {
            res = positiveMomentsDifference(base, other);
        } else {
            res = positiveMomentsDifference(other, base);
            res.milliseconds = -res.milliseconds;
            res.months = -res.months;
        }

        return res;
    }

    function createAdder(direction, name) {
        return function (val, period) {
            var dur, tmp;
            //invert the arguments, but complain about it
            if (period !== null && !isNaN(+period)) {
                deprecateSimple(name, 'moment().' + name  + '(period, number) is deprecated. Please use moment().' + name + '(number, period).');
                tmp = val; val = period; period = tmp;
            }

            val = typeof val === 'string' ? +val : val;
            dur = create__createDuration(val, period);
            add_subtract__addSubtract(this, dur, direction);
            return this;
        };
    }

    function add_subtract__addSubtract (mom, duration, isAdding, updateOffset) {
        var milliseconds = duration._milliseconds,
            days = duration._days,
            months = duration._months;
        updateOffset = updateOffset == null ? true : updateOffset;

        if (milliseconds) {
            mom._d.setTime(+mom._d + milliseconds * isAdding);
        }
        if (days) {
            get_set__set(mom, 'Date', get_set__get(mom, 'Date') + days * isAdding);
        }
        if (months) {
            setMonth(mom, get_set__get(mom, 'Month') + months * isAdding);
        }
        if (updateOffset) {
            utils_hooks__hooks.updateOffset(mom, days || months);
        }
    }

    var add_subtract__add      = createAdder(1, 'add');
    var add_subtract__subtract = createAdder(-1, 'subtract');

    function moment_calendar__calendar (time, formats) {
        // We want to compare the start of today, vs this.
        // Getting start-of-today depends on whether we're local/utc/offset or not.
        var now = time || local__createLocal(),
            sod = cloneWithOffset(now, this).startOf('day'),
            diff = this.diff(sod, 'days', true),
            format = diff < -6 ? 'sameElse' :
                diff < -1 ? 'lastWeek' :
                diff < 0 ? 'lastDay' :
                diff < 1 ? 'sameDay' :
                diff < 2 ? 'nextDay' :
                diff < 7 ? 'nextWeek' : 'sameElse';
        return this.format(formats && formats[format] || this.localeData().calendar(format, this, local__createLocal(now)));
    }

    function clone () {
        return new Moment(this);
    }

    function isAfter (input, units) {
        var inputMs;
        units = normalizeUnits(typeof units !== 'undefined' ? units : 'millisecond');
        if (units === 'millisecond') {
            input = isMoment(input) ? input : local__createLocal(input);
            return +this > +input;
        } else {
            inputMs = isMoment(input) ? +input : +local__createLocal(input);
            return inputMs < +this.clone().startOf(units);
        }
    }

    function isBefore (input, units) {
        var inputMs;
        units = normalizeUnits(typeof units !== 'undefined' ? units : 'millisecond');
        if (units === 'millisecond') {
            input = isMoment(input) ? input : local__createLocal(input);
            return +this < +input;
        } else {
            inputMs = isMoment(input) ? +input : +local__createLocal(input);
            return +this.clone().endOf(units) < inputMs;
        }
    }

    function isBetween (from, to, units) {
        return this.isAfter(from, units) && this.isBefore(to, units);
    }

    function isSame (input, units) {
        var inputMs;
        units = normalizeUnits(units || 'millisecond');
        if (units === 'millisecond') {
            input = isMoment(input) ? input : local__createLocal(input);
            return +this === +input;
        } else {
            inputMs = +local__createLocal(input);
            return +(this.clone().startOf(units)) <= inputMs && inputMs <= +(this.clone().endOf(units));
        }
    }

    function diff (input, units, asFloat) {
        var that = cloneWithOffset(input, this),
            zoneDelta = (that.utcOffset() - this.utcOffset()) * 6e4,
            delta, output;

        units = normalizeUnits(units);

        if (units === 'year' || units === 'month' || units === 'quarter') {
            output = monthDiff(this, that);
            if (units === 'quarter') {
                output = output / 3;
            } else if (units === 'year') {
                output = output / 12;
            }
        } else {
            delta = this - that;
            output = units === 'second' ? delta / 1e3 : // 1000
                units === 'minute' ? delta / 6e4 : // 1000 * 60
                units === 'hour' ? delta / 36e5 : // 1000 * 60 * 60
                units === 'day' ? (delta - zoneDelta) / 864e5 : // 1000 * 60 * 60 * 24, negate dst
                units === 'week' ? (delta - zoneDelta) / 6048e5 : // 1000 * 60 * 60 * 24 * 7, negate dst
                delta;
        }
        return asFloat ? output : absFloor(output);
    }

    function monthDiff (a, b) {
        // difference in months
        var wholeMonthDiff = ((b.year() - a.year()) * 12) + (b.month() - a.month()),
            // b is in (anchor - 1 month, anchor + 1 month)
            anchor = a.clone().add(wholeMonthDiff, 'months'),
            anchor2, adjust;

        if (b - anchor < 0) {
            anchor2 = a.clone().add(wholeMonthDiff - 1, 'months');
            // linear across the month
            adjust = (b - anchor) / (anchor - anchor2);
        } else {
            anchor2 = a.clone().add(wholeMonthDiff + 1, 'months');
            // linear across the month
            adjust = (b - anchor) / (anchor2 - anchor);
        }

        return -(wholeMonthDiff + adjust);
    }

    utils_hooks__hooks.defaultFormat = 'YYYY-MM-DDTHH:mm:ssZ';

    function toString () {
        return this.clone().locale('en').format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');
    }

    function moment_format__toISOString () {
        var m = this.clone().utc();
        if (0 < m.year() && m.year() <= 9999) {
            if ('function' === typeof Date.prototype.toISOString) {
                // native implementation is ~50x faster, use it when we can
                return this.toDate().toISOString();
            } else {
                return formatMoment(m, 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]');
            }
        } else {
            return formatMoment(m, 'YYYYYY-MM-DD[T]HH:mm:ss.SSS[Z]');
        }
    }

    function format (inputString) {
        var output = formatMoment(this, inputString || utils_hooks__hooks.defaultFormat);
        return this.localeData().postformat(output);
    }

    function from (time, withoutSuffix) {
        if (!this.isValid()) {
            return this.localeData().invalidDate();
        }
        return create__createDuration({to: this, from: time}).locale(this.locale()).humanize(!withoutSuffix);
    }

    function fromNow (withoutSuffix) {
        return this.from(local__createLocal(), withoutSuffix);
    }

    function to (time, withoutSuffix) {
        if (!this.isValid()) {
            return this.localeData().invalidDate();
        }
        return create__createDuration({from: this, to: time}).locale(this.locale()).humanize(!withoutSuffix);
    }

    function toNow (withoutSuffix) {
        return this.to(local__createLocal(), withoutSuffix);
    }

    function locale (key) {
        var newLocaleData;

        if (key === undefined) {
            return this._locale._abbr;
        } else {
            newLocaleData = locale_locales__getLocale(key);
            if (newLocaleData != null) {
                this._locale = newLocaleData;
            }
            return this;
        }
    }

    var lang = deprecate(
        'moment().lang() is deprecated. Instead, use moment().localeData() to get the language configuration. Use moment().locale() to change languages.',
        function (key) {
            if (key === undefined) {
                return this.localeData();
            } else {
                return this.locale(key);
            }
        }
    );

    function localeData () {
        return this._locale;
    }

    function startOf (units) {
        units = normalizeUnits(units);
        // the following switch intentionally omits break keywords
        // to utilize falling through the cases.
        switch (units) {
        case 'year':
            this.month(0);
            /* falls through */
        case 'quarter':
        case 'month':
            this.date(1);
            /* falls through */
        case 'week':
        case 'isoWeek':
        case 'day':
            this.hours(0);
            /* falls through */
        case 'hour':
            this.minutes(0);
            /* falls through */
        case 'minute':
            this.seconds(0);
            /* falls through */
        case 'second':
            this.milliseconds(0);
        }

        // weeks are a special case
        if (units === 'week') {
            this.weekday(0);
        }
        if (units === 'isoWeek') {
            this.isoWeekday(1);
        }

        // quarters are also special
        if (units === 'quarter') {
            this.month(Math.floor(this.month() / 3) * 3);
        }

        return this;
    }

    function endOf (units) {
        units = normalizeUnits(units);
        if (units === undefined || units === 'millisecond') {
            return this;
        }
        return this.startOf(units).add(1, (units === 'isoWeek' ? 'week' : units)).subtract(1, 'ms');
    }

    function to_type__valueOf () {
        return +this._d - ((this._offset || 0) * 60000);
    }

    function unix () {
        return Math.floor(+this / 1000);
    }

    function toDate () {
        return this._offset ? new Date(+this) : this._d;
    }

    function toArray () {
        var m = this;
        return [m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second(), m.millisecond()];
    }

    function toObject () {
        var m = this;
        return {
            years: m.year(),
            months: m.month(),
            date: m.date(),
            hours: m.hours(),
            minutes: m.minutes(),
            seconds: m.seconds(),
            milliseconds: m.milliseconds()
        };
    }

    function moment_valid__isValid () {
        return valid__isValid(this);
    }

    function parsingFlags () {
        return extend({}, getParsingFlags(this));
    }

    function invalidAt () {
        return getParsingFlags(this).overflow;
    }

    addFormatToken(0, ['gg', 2], 0, function () {
        return this.weekYear() % 100;
    });

    addFormatToken(0, ['GG', 2], 0, function () {
        return this.isoWeekYear() % 100;
    });

    function addWeekYearFormatToken (token, getter) {
        addFormatToken(0, [token, token.length], 0, getter);
    }

    addWeekYearFormatToken('gggg',     'weekYear');
    addWeekYearFormatToken('ggggg',    'weekYear');
    addWeekYearFormatToken('GGGG',  'isoWeekYear');
    addWeekYearFormatToken('GGGGG', 'isoWeekYear');

    // ALIASES

    addUnitAlias('weekYear', 'gg');
    addUnitAlias('isoWeekYear', 'GG');

    // PARSING

    addRegexToken('G',      matchSigned);
    addRegexToken('g',      matchSigned);
    addRegexToken('GG',     match1to2, match2);
    addRegexToken('gg',     match1to2, match2);
    addRegexToken('GGGG',   match1to4, match4);
    addRegexToken('gggg',   match1to4, match4);
    addRegexToken('GGGGG',  match1to6, match6);
    addRegexToken('ggggg',  match1to6, match6);

    addWeekParseToken(['gggg', 'ggggg', 'GGGG', 'GGGGG'], function (input, week, config, token) {
        week[token.substr(0, 2)] = toInt(input);
    });

    addWeekParseToken(['gg', 'GG'], function (input, week, config, token) {
        week[token] = utils_hooks__hooks.parseTwoDigitYear(input);
    });

    // HELPERS

    function weeksInYear(year, dow, doy) {
        return weekOfYear(local__createLocal([year, 11, 31 + dow - doy]), dow, doy).week;
    }

    // MOMENTS

    function getSetWeekYear (input) {
        var year = weekOfYear(this, this.localeData()._week.dow, this.localeData()._week.doy).year;
        return input == null ? year : this.add((input - year), 'y');
    }

    function getSetISOWeekYear (input) {
        var year = weekOfYear(this, 1, 4).year;
        return input == null ? year : this.add((input - year), 'y');
    }

    function getISOWeeksInYear () {
        return weeksInYear(this.year(), 1, 4);
    }

    function getWeeksInYear () {
        var weekInfo = this.localeData()._week;
        return weeksInYear(this.year(), weekInfo.dow, weekInfo.doy);
    }

    addFormatToken('Q', 0, 0, 'quarter');

    // ALIASES

    addUnitAlias('quarter', 'Q');

    // PARSING

    addRegexToken('Q', match1);
    addParseToken('Q', function (input, array) {
        array[MONTH] = (toInt(input) - 1) * 3;
    });

    // MOMENTS

    function getSetQuarter (input) {
        return input == null ? Math.ceil((this.month() + 1) / 3) : this.month((input - 1) * 3 + this.month() % 3);
    }

    addFormatToken('D', ['DD', 2], 'Do', 'date');

    // ALIASES

    addUnitAlias('date', 'D');

    // PARSING

    addRegexToken('D',  match1to2);
    addRegexToken('DD', match1to2, match2);
    addRegexToken('Do', function (isStrict, locale) {
        return isStrict ? locale._ordinalParse : locale._ordinalParseLenient;
    });

    addParseToken(['D', 'DD'], DATE);
    addParseToken('Do', function (input, array) {
        array[DATE] = toInt(input.match(match1to2)[0], 10);
    });

    // MOMENTS

    var getSetDayOfMonth = makeGetSet('Date', true);

    addFormatToken('d', 0, 'do', 'day');

    addFormatToken('dd', 0, 0, function (format) {
        return this.localeData().weekdaysMin(this, format);
    });

    addFormatToken('ddd', 0, 0, function (format) {
        return this.localeData().weekdaysShort(this, format);
    });

    addFormatToken('dddd', 0, 0, function (format) {
        return this.localeData().weekdays(this, format);
    });

    addFormatToken('e', 0, 0, 'weekday');
    addFormatToken('E', 0, 0, 'isoWeekday');

    // ALIASES

    addUnitAlias('day', 'd');
    addUnitAlias('weekday', 'e');
    addUnitAlias('isoWeekday', 'E');

    // PARSING

    addRegexToken('d',    match1to2);
    addRegexToken('e',    match1to2);
    addRegexToken('E',    match1to2);
    addRegexToken('dd',   matchWord);
    addRegexToken('ddd',  matchWord);
    addRegexToken('dddd', matchWord);

    addWeekParseToken(['dd', 'ddd', 'dddd'], function (input, week, config) {
        var weekday = config._locale.weekdaysParse(input);
        // if we didn't get a weekday name, mark the date as invalid
        if (weekday != null) {
            week.d = weekday;
        } else {
            getParsingFlags(config).invalidWeekday = input;
        }
    });

    addWeekParseToken(['d', 'e', 'E'], function (input, week, config, token) {
        week[token] = toInt(input);
    });

    // HELPERS

    function parseWeekday(input, locale) {
        if (typeof input !== 'string') {
            return input;
        }

        if (!isNaN(input)) {
            return parseInt(input, 10);
        }

        input = locale.weekdaysParse(input);
        if (typeof input === 'number') {
            return input;
        }

        return null;
    }

    // LOCALES

    var defaultLocaleWeekdays = 'Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday'.split('_');
    function localeWeekdays (m) {
        return this._weekdays[m.day()];
    }

    var defaultLocaleWeekdaysShort = 'Sun_Mon_Tue_Wed_Thu_Fri_Sat'.split('_');
    function localeWeekdaysShort (m) {
        return this._weekdaysShort[m.day()];
    }

    var defaultLocaleWeekdaysMin = 'Su_Mo_Tu_We_Th_Fr_Sa'.split('_');
    function localeWeekdaysMin (m) {
        return this._weekdaysMin[m.day()];
    }

    function localeWeekdaysParse (weekdayName) {
        var i, mom, regex;

        this._weekdaysParse = this._weekdaysParse || [];

        for (i = 0; i < 7; i++) {
            // make the regex if we don't have it already
            if (!this._weekdaysParse[i]) {
                mom = local__createLocal([2000, 1]).day(i);
                regex = '^' + this.weekdays(mom, '') + '|^' + this.weekdaysShort(mom, '') + '|^' + this.weekdaysMin(mom, '');
                this._weekdaysParse[i] = new RegExp(regex.replace('.', ''), 'i');
            }
            // test the regex
            if (this._weekdaysParse[i].test(weekdayName)) {
                return i;
            }
        }
    }

    // MOMENTS

    function getSetDayOfWeek (input) {
        var day = this._isUTC ? this._d.getUTCDay() : this._d.getDay();
        if (input != null) {
            input = parseWeekday(input, this.localeData());
            return this.add(input - day, 'd');
        } else {
            return day;
        }
    }

    function getSetLocaleDayOfWeek (input) {
        var weekday = (this.day() + 7 - this.localeData()._week.dow) % 7;
        return input == null ? weekday : this.add(input - weekday, 'd');
    }

    function getSetISODayOfWeek (input) {
        // behaves the same as moment#day except
        // as a getter, returns 7 instead of 0 (1-7 range instead of 0-6)
        // as a setter, sunday should belong to the previous week.
        return input == null ? this.day() || 7 : this.day(this.day() % 7 ? input : input - 7);
    }

    addFormatToken('H', ['HH', 2], 0, 'hour');
    addFormatToken('h', ['hh', 2], 0, function () {
        return this.hours() % 12 || 12;
    });

    function meridiem (token, lowercase) {
        addFormatToken(token, 0, 0, function () {
            return this.localeData().meridiem(this.hours(), this.minutes(), lowercase);
        });
    }

    meridiem('a', true);
    meridiem('A', false);

    // ALIASES

    addUnitAlias('hour', 'h');

    // PARSING

    function matchMeridiem (isStrict, locale) {
        return locale._meridiemParse;
    }

    addRegexToken('a',  matchMeridiem);
    addRegexToken('A',  matchMeridiem);
    addRegexToken('H',  match1to2);
    addRegexToken('h',  match1to2);
    addRegexToken('HH', match1to2, match2);
    addRegexToken('hh', match1to2, match2);

    addParseToken(['H', 'HH'], HOUR);
    addParseToken(['a', 'A'], function (input, array, config) {
        config._isPm = config._locale.isPM(input);
        config._meridiem = input;
    });
    addParseToken(['h', 'hh'], function (input, array, config) {
        array[HOUR] = toInt(input);
        getParsingFlags(config).bigHour = true;
    });

    // LOCALES

    function localeIsPM (input) {
        // IE8 Quirks Mode & IE7 Standards Mode do not allow accessing strings like arrays
        // Using charAt should be more compatible.
        return ((input + '').toLowerCase().charAt(0) === 'p');
    }

    var defaultLocaleMeridiemParse = /[ap]\.?m?\.?/i;
    function localeMeridiem (hours, minutes, isLower) {
        if (hours > 11) {
            return isLower ? 'pm' : 'PM';
        } else {
            return isLower ? 'am' : 'AM';
        }
    }


    // MOMENTS

    // Setting the hour should keep the time, because the user explicitly
    // specified which hour he wants. So trying to maintain the same hour (in
    // a new timezone) makes sense. Adding/subtracting hours does not follow
    // this rule.
    var getSetHour = makeGetSet('Hours', true);

    addFormatToken('m', ['mm', 2], 0, 'minute');

    // ALIASES

    addUnitAlias('minute', 'm');

    // PARSING

    addRegexToken('m',  match1to2);
    addRegexToken('mm', match1to2, match2);
    addParseToken(['m', 'mm'], MINUTE);

    // MOMENTS

    var getSetMinute = makeGetSet('Minutes', false);

    addFormatToken('s', ['ss', 2], 0, 'second');

    // ALIASES

    addUnitAlias('second', 's');

    // PARSING

    addRegexToken('s',  match1to2);
    addRegexToken('ss', match1to2, match2);
    addParseToken(['s', 'ss'], SECOND);

    // MOMENTS

    var getSetSecond = makeGetSet('Seconds', false);

    addFormatToken('S', 0, 0, function () {
        return ~~(this.millisecond() / 100);
    });

    addFormatToken(0, ['SS', 2], 0, function () {
        return ~~(this.millisecond() / 10);
    });

    addFormatToken(0, ['SSS', 3], 0, 'millisecond');
    addFormatToken(0, ['SSSS', 4], 0, function () {
        return this.millisecond() * 10;
    });
    addFormatToken(0, ['SSSSS', 5], 0, function () {
        return this.millisecond() * 100;
    });
    addFormatToken(0, ['SSSSSS', 6], 0, function () {
        return this.millisecond() * 1000;
    });
    addFormatToken(0, ['SSSSSSS', 7], 0, function () {
        return this.millisecond() * 10000;
    });
    addFormatToken(0, ['SSSSSSSS', 8], 0, function () {
        return this.millisecond() * 100000;
    });
    addFormatToken(0, ['SSSSSSSSS', 9], 0, function () {
        return this.millisecond() * 1000000;
    });


    // ALIASES

    addUnitAlias('millisecond', 'ms');

    // PARSING

    addRegexToken('S',    match1to3, match1);
    addRegexToken('SS',   match1to3, match2);
    addRegexToken('SSS',  match1to3, match3);

    var token;
    for (token = 'SSSS'; token.length <= 9; token += 'S') {
        addRegexToken(token, matchUnsigned);
    }

    function parseMs(input, array) {
        array[MILLISECOND] = toInt(('0.' + input) * 1000);
    }

    for (token = 'S'; token.length <= 9; token += 'S') {
        addParseToken(token, parseMs);
    }
    // MOMENTS

    var getSetMillisecond = makeGetSet('Milliseconds', false);

    addFormatToken('z',  0, 0, 'zoneAbbr');
    addFormatToken('zz', 0, 0, 'zoneName');

    // MOMENTS

    function getZoneAbbr () {
        return this._isUTC ? 'UTC' : '';
    }

    function getZoneName () {
        return this._isUTC ? 'Coordinated Universal Time' : '';
    }

    var momentPrototype__proto = Moment.prototype;

    momentPrototype__proto.add          = add_subtract__add;
    momentPrototype__proto.calendar     = moment_calendar__calendar;
    momentPrototype__proto.clone        = clone;
    momentPrototype__proto.diff         = diff;
    momentPrototype__proto.endOf        = endOf;
    momentPrototype__proto.format       = format;
    momentPrototype__proto.from         = from;
    momentPrototype__proto.fromNow      = fromNow;
    momentPrototype__proto.to           = to;
    momentPrototype__proto.toNow        = toNow;
    momentPrototype__proto.get          = getSet;
    momentPrototype__proto.invalidAt    = invalidAt;
    momentPrototype__proto.isAfter      = isAfter;
    momentPrototype__proto.isBefore     = isBefore;
    momentPrototype__proto.isBetween    = isBetween;
    momentPrototype__proto.isSame       = isSame;
    momentPrototype__proto.isValid      = moment_valid__isValid;
    momentPrototype__proto.lang         = lang;
    momentPrototype__proto.locale       = locale;
    momentPrototype__proto.localeData   = localeData;
    momentPrototype__proto.max          = prototypeMax;
    momentPrototype__proto.min          = prototypeMin;
    momentPrototype__proto.parsingFlags = parsingFlags;
    momentPrototype__proto.set          = getSet;
    momentPrototype__proto.startOf      = startOf;
    momentPrototype__proto.subtract     = add_subtract__subtract;
    momentPrototype__proto.toArray      = toArray;
    momentPrototype__proto.toObject     = toObject;
    momentPrototype__proto.toDate       = toDate;
    momentPrototype__proto.toISOString  = moment_format__toISOString;
    momentPrototype__proto.toJSON       = moment_format__toISOString;
    momentPrototype__proto.toString     = toString;
    momentPrototype__proto.unix         = unix;
    momentPrototype__proto.valueOf      = to_type__valueOf;

    // Year
    momentPrototype__proto.year       = getSetYear;
    momentPrototype__proto.isLeapYear = getIsLeapYear;

    // Week Year
    momentPrototype__proto.weekYear    = getSetWeekYear;
    momentPrototype__proto.isoWeekYear = getSetISOWeekYear;

    // Quarter
    momentPrototype__proto.quarter = momentPrototype__proto.quarters = getSetQuarter;

    // Month
    momentPrototype__proto.month       = getSetMonth;
    momentPrototype__proto.daysInMonth = getDaysInMonth;

    // Week
    momentPrototype__proto.week           = momentPrototype__proto.weeks        = getSetWeek;
    momentPrototype__proto.isoWeek        = momentPrototype__proto.isoWeeks     = getSetISOWeek;
    momentPrototype__proto.weeksInYear    = getWeeksInYear;
    momentPrototype__proto.isoWeeksInYear = getISOWeeksInYear;

    // Day
    momentPrototype__proto.date       = getSetDayOfMonth;
    momentPrototype__proto.day        = momentPrototype__proto.days             = getSetDayOfWeek;
    momentPrototype__proto.weekday    = getSetLocaleDayOfWeek;
    momentPrototype__proto.isoWeekday = getSetISODayOfWeek;
    momentPrototype__proto.dayOfYear  = getSetDayOfYear;

    // Hour
    momentPrototype__proto.hour = momentPrototype__proto.hours = getSetHour;

    // Minute
    momentPrototype__proto.minute = momentPrototype__proto.minutes = getSetMinute;

    // Second
    momentPrototype__proto.second = momentPrototype__proto.seconds = getSetSecond;

    // Millisecond
    momentPrototype__proto.millisecond = momentPrototype__proto.milliseconds = getSetMillisecond;

    // Offset
    momentPrototype__proto.utcOffset            = getSetOffset;
    momentPrototype__proto.utc                  = setOffsetToUTC;
    momentPrototype__proto.local                = setOffsetToLocal;
    momentPrototype__proto.parseZone            = setOffsetToParsedOffset;
    momentPrototype__proto.hasAlignedHourOffset = hasAlignedHourOffset;
    momentPrototype__proto.isDST                = isDaylightSavingTime;
    momentPrototype__proto.isDSTShifted         = isDaylightSavingTimeShifted;
    momentPrototype__proto.isLocal              = isLocal;
    momentPrototype__proto.isUtcOffset          = isUtcOffset;
    momentPrototype__proto.isUtc                = isUtc;
    momentPrototype__proto.isUTC                = isUtc;

    // Timezone
    momentPrototype__proto.zoneAbbr = getZoneAbbr;
    momentPrototype__proto.zoneName = getZoneName;

    // Deprecations
    momentPrototype__proto.dates  = deprecate('dates accessor is deprecated. Use date instead.', getSetDayOfMonth);
    momentPrototype__proto.months = deprecate('months accessor is deprecated. Use month instead', getSetMonth);
    momentPrototype__proto.years  = deprecate('years accessor is deprecated. Use year instead', getSetYear);
    momentPrototype__proto.zone   = deprecate('moment().zone is deprecated, use moment().utcOffset instead. https://github.com/moment/moment/issues/1779', getSetZone);

    var momentPrototype = momentPrototype__proto;

    function moment__createUnix (input) {
        return local__createLocal(input * 1000);
    }

    function moment__createInZone () {
        return local__createLocal.apply(null, arguments).parseZone();
    }

    var defaultCalendar = {
        sameDay : '[Today at] LT',
        nextDay : '[Tomorrow at] LT',
        nextWeek : 'dddd [at] LT',
        lastDay : '[Yesterday at] LT',
        lastWeek : '[Last] dddd [at] LT',
        sameElse : 'L'
    };

    function locale_calendar__calendar (key, mom, now) {
        var output = this._calendar[key];
        return typeof output === 'function' ? output.call(mom, now) : output;
    }

    var defaultLongDateFormat = {
        LTS  : 'h:mm:ss A',
        LT   : 'h:mm A',
        L    : 'MM/DD/YYYY',
        LL   : 'MMMM D, YYYY',
        LLL  : 'MMMM D, YYYY h:mm A',
        LLLL : 'dddd, MMMM D, YYYY h:mm A'
    };

    function longDateFormat (key) {
        var format = this._longDateFormat[key],
            formatUpper = this._longDateFormat[key.toUpperCase()];

        if (format || !formatUpper) {
            return format;
        }

        this._longDateFormat[key] = formatUpper.replace(/MMMM|MM|DD|dddd/g, function (val) {
            return val.slice(1);
        });

        return this._longDateFormat[key];
    }

    var defaultInvalidDate = 'Invalid date';

    function invalidDate () {
        return this._invalidDate;
    }

    var defaultOrdinal = '%d';
    var defaultOrdinalParse = /\d{1,2}/;

    function ordinal (number) {
        return this._ordinal.replace('%d', number);
    }

    function preParsePostFormat (string) {
        return string;
    }

    var defaultRelativeTime = {
        future : 'in %s',
        past   : '%s ago',
        s  : 'a few seconds',
        m  : 'a minute',
        mm : '%d minutes',
        h  : 'an hour',
        hh : '%d hours',
        d  : 'a day',
        dd : '%d days',
        M  : 'a month',
        MM : '%d months',
        y  : 'a year',
        yy : '%d years'
    };

    function relative__relativeTime (number, withoutSuffix, string, isFuture) {
        var output = this._relativeTime[string];
        return (typeof output === 'function') ?
            output(number, withoutSuffix, string, isFuture) :
            output.replace(/%d/i, number);
    }

    function pastFuture (diff, output) {
        var format = this._relativeTime[diff > 0 ? 'future' : 'past'];
        return typeof format === 'function' ? format(output) : format.replace(/%s/i, output);
    }

    function locale_set__set (config) {
        var prop, i;
        for (i in config) {
            prop = config[i];
            if (typeof prop === 'function') {
                this[i] = prop;
            } else {
                this['_' + i] = prop;
            }
        }
        // Lenient ordinal parsing accepts just a number in addition to
        // number + (possibly) stuff coming from _ordinalParseLenient.
        this._ordinalParseLenient = new RegExp(this._ordinalParse.source + '|' + (/\d{1,2}/).source);
    }

    var prototype__proto = Locale.prototype;

    prototype__proto._calendar       = defaultCalendar;
    prototype__proto.calendar        = locale_calendar__calendar;
    prototype__proto._longDateFormat = defaultLongDateFormat;
    prototype__proto.longDateFormat  = longDateFormat;
    prototype__proto._invalidDate    = defaultInvalidDate;
    prototype__proto.invalidDate     = invalidDate;
    prototype__proto._ordinal        = defaultOrdinal;
    prototype__proto.ordinal         = ordinal;
    prototype__proto._ordinalParse   = defaultOrdinalParse;
    prototype__proto.preparse        = preParsePostFormat;
    prototype__proto.postformat      = preParsePostFormat;
    prototype__proto._relativeTime   = defaultRelativeTime;
    prototype__proto.relativeTime    = relative__relativeTime;
    prototype__proto.pastFuture      = pastFuture;
    prototype__proto.set             = locale_set__set;

    // Month
    prototype__proto.months       =        localeMonths;
    prototype__proto._months      = defaultLocaleMonths;
    prototype__proto.monthsShort  =        localeMonthsShort;
    prototype__proto._monthsShort = defaultLocaleMonthsShort;
    prototype__proto.monthsParse  =        localeMonthsParse;

    // Week
    prototype__proto.week = localeWeek;
    prototype__proto._week = defaultLocaleWeek;
    prototype__proto.firstDayOfYear = localeFirstDayOfYear;
    prototype__proto.firstDayOfWeek = localeFirstDayOfWeek;

    // Day of Week
    prototype__proto.weekdays       =        localeWeekdays;
    prototype__proto._weekdays      = defaultLocaleWeekdays;
    prototype__proto.weekdaysMin    =        localeWeekdaysMin;
    prototype__proto._weekdaysMin   = defaultLocaleWeekdaysMin;
    prototype__proto.weekdaysShort  =        localeWeekdaysShort;
    prototype__proto._weekdaysShort = defaultLocaleWeekdaysShort;
    prototype__proto.weekdaysParse  =        localeWeekdaysParse;

    // Hours
    prototype__proto.isPM = localeIsPM;
    prototype__proto._meridiemParse = defaultLocaleMeridiemParse;
    prototype__proto.meridiem = localeMeridiem;

    function lists__get (format, index, field, setter) {
        var locale = locale_locales__getLocale();
        var utc = create_utc__createUTC().set(setter, index);
        return locale[field](utc, format);
    }

    function list (format, index, field, count, setter) {
        if (typeof format === 'number') {
            index = format;
            format = undefined;
        }

        format = format || '';

        if (index != null) {
            return lists__get(format, index, field, setter);
        }

        var i;
        var out = [];
        for (i = 0; i < count; i++) {
            out[i] = lists__get(format, i, field, setter);
        }
        return out;
    }

    function lists__listMonths (format, index) {
        return list(format, index, 'months', 12, 'month');
    }

    function lists__listMonthsShort (format, index) {
        return list(format, index, 'monthsShort', 12, 'month');
    }

    function lists__listWeekdays (format, index) {
        return list(format, index, 'weekdays', 7, 'day');
    }

    function lists__listWeekdaysShort (format, index) {
        return list(format, index, 'weekdaysShort', 7, 'day');
    }

    function lists__listWeekdaysMin (format, index) {
        return list(format, index, 'weekdaysMin', 7, 'day');
    }

    locale_locales__getSetGlobalLocale('en', {
        ordinalParse: /\d{1,2}(th|st|nd|rd)/,
        ordinal : function (number) {
            var b = number % 10,
                output = (toInt(number % 100 / 10) === 1) ? 'th' :
                (b === 1) ? 'st' :
                (b === 2) ? 'nd' :
                (b === 3) ? 'rd' : 'th';
            return number + output;
        }
    });

    // Side effect imports
    utils_hooks__hooks.lang = deprecate('moment.lang is deprecated. Use moment.locale instead.', locale_locales__getSetGlobalLocale);
    utils_hooks__hooks.langData = deprecate('moment.langData is deprecated. Use moment.localeData instead.', locale_locales__getLocale);

    var mathAbs = Math.abs;

    function duration_abs__abs () {
        var data           = this._data;

        this._milliseconds = mathAbs(this._milliseconds);
        this._days         = mathAbs(this._days);
        this._months       = mathAbs(this._months);

        data.milliseconds  = mathAbs(data.milliseconds);
        data.seconds       = mathAbs(data.seconds);
        data.minutes       = mathAbs(data.minutes);
        data.hours         = mathAbs(data.hours);
        data.months        = mathAbs(data.months);
        data.years         = mathAbs(data.years);

        return this;
    }

    function duration_add_subtract__addSubtract (duration, input, value, direction) {
        var other = create__createDuration(input, value);

        duration._milliseconds += direction * other._milliseconds;
        duration._days         += direction * other._days;
        duration._months       += direction * other._months;

        return duration._bubble();
    }

    // supports only 2.0-style add(1, 's') or add(duration)
    function duration_add_subtract__add (input, value) {
        return duration_add_subtract__addSubtract(this, input, value, 1);
    }

    // supports only 2.0-style subtract(1, 's') or subtract(duration)
    function duration_add_subtract__subtract (input, value) {
        return duration_add_subtract__addSubtract(this, input, value, -1);
    }

    function absCeil (number) {
        if (number < 0) {
            return Math.floor(number);
        } else {
            return Math.ceil(number);
        }
    }

    function bubble () {
        var milliseconds = this._milliseconds;
        var days         = this._days;
        var months       = this._months;
        var data         = this._data;
        var seconds, minutes, hours, years, monthsFromDays;

        // if we have a mix of positive and negative values, bubble down first
        // check: https://github.com/moment/moment/issues/2166
        if (!((milliseconds >= 0 && days >= 0 && months >= 0) ||
                (milliseconds <= 0 && days <= 0 && months <= 0))) {
            milliseconds += absCeil(monthsToDays(months) + days) * 864e5;
            days = 0;
            months = 0;
        }

        // The following code bubbles up values, see the tests for
        // examples of what that means.
        data.milliseconds = milliseconds % 1000;

        seconds           = absFloor(milliseconds / 1000);
        data.seconds      = seconds % 60;

        minutes           = absFloor(seconds / 60);
        data.minutes      = minutes % 60;

        hours             = absFloor(minutes / 60);
        data.hours        = hours % 24;

        days += absFloor(hours / 24);

        // convert days to months
        monthsFromDays = absFloor(daysToMonths(days));
        months += monthsFromDays;
        days -= absCeil(monthsToDays(monthsFromDays));

        // 12 months -> 1 year
        years = absFloor(months / 12);
        months %= 12;

        data.days   = days;
        data.months = months;
        data.years  = years;

        return this;
    }

    function daysToMonths (days) {
        // 400 years have 146097 days (taking into account leap year rules)
        // 400 years have 12 months === 4800
        return days * 4800 / 146097;
    }

    function monthsToDays (months) {
        // the reverse of daysToMonths
        return months * 146097 / 4800;
    }

    function as (units) {
        var days;
        var months;
        var milliseconds = this._milliseconds;

        units = normalizeUnits(units);

        if (units === 'month' || units === 'year') {
            days   = this._days   + milliseconds / 864e5;
            months = this._months + daysToMonths(days);
            return units === 'month' ? months : months / 12;
        } else {
            // handle milliseconds separately because of floating point math errors (issue #1867)
            days = this._days + Math.round(monthsToDays(this._months));
            switch (units) {
                case 'week'   : return days / 7     + milliseconds / 6048e5;
                case 'day'    : return days         + milliseconds / 864e5;
                case 'hour'   : return days * 24    + milliseconds / 36e5;
                case 'minute' : return days * 1440  + milliseconds / 6e4;
                case 'second' : return days * 86400 + milliseconds / 1000;
                // Math.floor prevents floating point math errors here
                case 'millisecond': return Math.floor(days * 864e5) + milliseconds;
                default: throw new Error('Unknown unit ' + units);
            }
        }
    }

    // TODO: Use this.as('ms')?
    function duration_as__valueOf () {
        return (
            this._milliseconds +
            this._days * 864e5 +
            (this._months % 12) * 2592e6 +
            toInt(this._months / 12) * 31536e6
        );
    }

    function makeAs (alias) {
        return function () {
            return this.as(alias);
        };
    }

    var asMilliseconds = makeAs('ms');
    var asSeconds      = makeAs('s');
    var asMinutes      = makeAs('m');
    var asHours        = makeAs('h');
    var asDays         = makeAs('d');
    var asWeeks        = makeAs('w');
    var asMonths       = makeAs('M');
    var asYears        = makeAs('y');

    function duration_get__get (units) {
        units = normalizeUnits(units);
        return this[units + 's']();
    }

    function makeGetter(name) {
        return function () {
            return this._data[name];
        };
    }

    var milliseconds = makeGetter('milliseconds');
    var seconds      = makeGetter('seconds');
    var minutes      = makeGetter('minutes');
    var hours        = makeGetter('hours');
    var days         = makeGetter('days');
    var months       = makeGetter('months');
    var years        = makeGetter('years');

    function weeks () {
        return absFloor(this.days() / 7);
    }

    var round = Math.round;
    var thresholds = {
        s: 45,  // seconds to minute
        m: 45,  // minutes to hour
        h: 22,  // hours to day
        d: 26,  // days to month
        M: 11   // months to year
    };

    // helper function for moment.fn.from, moment.fn.fromNow, and moment.duration.fn.humanize
    function substituteTimeAgo(string, number, withoutSuffix, isFuture, locale) {
        return locale.relativeTime(number || 1, !!withoutSuffix, string, isFuture);
    }

    function duration_humanize__relativeTime (posNegDuration, withoutSuffix, locale) {
        var duration = create__createDuration(posNegDuration).abs();
        var seconds  = round(duration.as('s'));
        var minutes  = round(duration.as('m'));
        var hours    = round(duration.as('h'));
        var days     = round(duration.as('d'));
        var months   = round(duration.as('M'));
        var years    = round(duration.as('y'));

        var a = seconds < thresholds.s && ['s', seconds]  ||
                minutes === 1          && ['m']           ||
                minutes < thresholds.m && ['mm', minutes] ||
                hours   === 1          && ['h']           ||
                hours   < thresholds.h && ['hh', hours]   ||
                days    === 1          && ['d']           ||
                days    < thresholds.d && ['dd', days]    ||
                months  === 1          && ['M']           ||
                months  < thresholds.M && ['MM', months]  ||
                years   === 1          && ['y']           || ['yy', years];

        a[2] = withoutSuffix;
        a[3] = +posNegDuration > 0;
        a[4] = locale;
        return substituteTimeAgo.apply(null, a);
    }

    // This function allows you to set a threshold for relative time strings
    function duration_humanize__getSetRelativeTimeThreshold (threshold, limit) {
        if (thresholds[threshold] === undefined) {
            return false;
        }
        if (limit === undefined) {
            return thresholds[threshold];
        }
        thresholds[threshold] = limit;
        return true;
    }

    function humanize (withSuffix) {
        var locale = this.localeData();
        var output = duration_humanize__relativeTime(this, !withSuffix, locale);

        if (withSuffix) {
            output = locale.pastFuture(+this, output);
        }

        return locale.postformat(output);
    }

    var iso_string__abs = Math.abs;

    function iso_string__toISOString() {
        // for ISO strings we do not use the normal bubbling rules:
        //  * milliseconds bubble up until they become hours
        //  * days do not bubble at all
        //  * months bubble up until they become years
        // This is because there is no context-free conversion between hours and days
        // (think of clock changes)
        // and also not between days and months (28-31 days per month)
        var seconds = iso_string__abs(this._milliseconds) / 1000;
        var days         = iso_string__abs(this._days);
        var months       = iso_string__abs(this._months);
        var minutes, hours, years;

        // 3600 seconds -> 60 minutes -> 1 hour
        minutes           = absFloor(seconds / 60);
        hours             = absFloor(minutes / 60);
        seconds %= 60;
        minutes %= 60;

        // 12 months -> 1 year
        years  = absFloor(months / 12);
        months %= 12;


        // inspired by https://github.com/dordille/moment-isoduration/blob/master/moment.isoduration.js
        var Y = years;
        var M = months;
        var D = days;
        var h = hours;
        var m = minutes;
        var s = seconds;
        var total = this.asSeconds();

        if (!total) {
            // this is the same as C#'s (Noda) and python (isodate)...
            // but not other JS (goog.date)
            return 'P0D';
        }

        return (total < 0 ? '-' : '') +
            'P' +
            (Y ? Y + 'Y' : '') +
            (M ? M + 'M' : '') +
            (D ? D + 'D' : '') +
            ((h || m || s) ? 'T' : '') +
            (h ? h + 'H' : '') +
            (m ? m + 'M' : '') +
            (s ? s + 'S' : '');
    }

    var duration_prototype__proto = Duration.prototype;

    duration_prototype__proto.abs            = duration_abs__abs;
    duration_prototype__proto.add            = duration_add_subtract__add;
    duration_prototype__proto.subtract       = duration_add_subtract__subtract;
    duration_prototype__proto.as             = as;
    duration_prototype__proto.asMilliseconds = asMilliseconds;
    duration_prototype__proto.asSeconds      = asSeconds;
    duration_prototype__proto.asMinutes      = asMinutes;
    duration_prototype__proto.asHours        = asHours;
    duration_prototype__proto.asDays         = asDays;
    duration_prototype__proto.asWeeks        = asWeeks;
    duration_prototype__proto.asMonths       = asMonths;
    duration_prototype__proto.asYears        = asYears;
    duration_prototype__proto.valueOf        = duration_as__valueOf;
    duration_prototype__proto._bubble        = bubble;
    duration_prototype__proto.get            = duration_get__get;
    duration_prototype__proto.milliseconds   = milliseconds;
    duration_prototype__proto.seconds        = seconds;
    duration_prototype__proto.minutes        = minutes;
    duration_prototype__proto.hours          = hours;
    duration_prototype__proto.days           = days;
    duration_prototype__proto.weeks          = weeks;
    duration_prototype__proto.months         = months;
    duration_prototype__proto.years          = years;
    duration_prototype__proto.humanize       = humanize;
    duration_prototype__proto.toISOString    = iso_string__toISOString;
    duration_prototype__proto.toString       = iso_string__toISOString;
    duration_prototype__proto.toJSON         = iso_string__toISOString;
    duration_prototype__proto.locale         = locale;
    duration_prototype__proto.localeData     = localeData;

    // Deprecations
    duration_prototype__proto.toIsoString = deprecate('toIsoString() is deprecated. Please use toISOString() instead (notice the capitals)', iso_string__toISOString);
    duration_prototype__proto.lang = lang;

    // Side effect imports

    addFormatToken('X', 0, 0, 'unix');
    addFormatToken('x', 0, 0, 'valueOf');

    // PARSING

    addRegexToken('x', matchSigned);
    addRegexToken('X', matchTimestamp);
    addParseToken('X', function (input, array, config) {
        config._d = new Date(parseFloat(input, 10) * 1000);
    });
    addParseToken('x', function (input, array, config) {
        config._d = new Date(toInt(input));
    });

    // Side effect imports


    utils_hooks__hooks.version = '2.10.6';

    setHookCallback(local__createLocal);

    utils_hooks__hooks.fn                    = momentPrototype;
    utils_hooks__hooks.min                   = min;
    utils_hooks__hooks.max                   = max;
    utils_hooks__hooks.utc                   = create_utc__createUTC;
    utils_hooks__hooks.unix                  = moment__createUnix;
    utils_hooks__hooks.months                = lists__listMonths;
    utils_hooks__hooks.isDate                = isDate;
    utils_hooks__hooks.locale                = locale_locales__getSetGlobalLocale;
    utils_hooks__hooks.invalid               = valid__createInvalid;
    utils_hooks__hooks.duration              = create__createDuration;
    utils_hooks__hooks.isMoment              = isMoment;
    utils_hooks__hooks.weekdays              = lists__listWeekdays;
    utils_hooks__hooks.parseZone             = moment__createInZone;
    utils_hooks__hooks.localeData            = locale_locales__getLocale;
    utils_hooks__hooks.isDuration            = isDuration;
    utils_hooks__hooks.monthsShort           = lists__listMonthsShort;
    utils_hooks__hooks.weekdaysMin           = lists__listWeekdaysMin;
    utils_hooks__hooks.defineLocale          = defineLocale;
    utils_hooks__hooks.weekdaysShort         = lists__listWeekdaysShort;
    utils_hooks__hooks.normalizeUnits        = normalizeUnits;
    utils_hooks__hooks.relativeTimeThreshold = duration_humanize__getSetRelativeTimeThreshold;

    var _moment = utils_hooks__hooks;

    return _moment;

}));
//! moment.js locale configuration
//! locale : brazilian portuguese (pt-br)
//! author : Caio Ribeiro Pereira : https://github.com/caio-ribeiro-pereira

(function (global, factory) {
   typeof exports === 'object' && typeof module !== 'undefined' ? factory(require('../moment')) :
   typeof define === 'function' && define.amd ? define(['moment'], factory) :
   factory(global.moment)
}(this, function (moment) { 'use strict';


    var pt_br = moment.defineLocale('pt-br', {
        months : 'Janeiro_Fevereiro_Março_Abril_Maio_Junho_Julho_Agosto_Setembro_Outubro_Novembro_Dezembro'.split('_'),
        monthsShort : 'Jan_Fev_Mar_Abr_Mai_Jun_Jul_Ago_Set_Out_Nov_Dez'.split('_'),
        weekdays : 'Domingo_Segunda-Feira_Terça-Feira_Quarta-Feira_Quinta-Feira_Sexta-Feira_Sábado'.split('_'),
        weekdaysShort : 'Dom_Seg_Ter_Qua_Qui_Sex_Sáb'.split('_'),
        weekdaysMin : 'Dom_2ª_3ª_4ª_5ª_6ª_Sáb'.split('_'),
        longDateFormat : {
            LT : 'HH:mm',
            LTS : 'HH:mm:ss',
            L : 'DD/MM/YYYY',
            LL : 'D [de] MMMM [de] YYYY',
            LLL : 'D [de] MMMM [de] YYYY [às] HH:mm',
            LLLL : 'dddd, D [de] MMMM [de] YYYY [às] HH:mm'
        },
        calendar : {
            sameDay: '[Hoje às] LT',
            nextDay: '[Amanhã às] LT',
            nextWeek: 'dddd [às] LT',
            lastDay: '[Ontem às] LT',
            lastWeek: function () {
                return (this.day() === 0 || this.day() === 6) ?
                    '[Último] dddd [às] LT' : // Saturday + Sunday
                    '[Última] dddd [às] LT'; // Monday - Friday
            },
            sameElse: 'L'
        },
        relativeTime : {
            future : 'em %s',
            past : '%s atrás',
            s : 'poucos segundos',
            m : 'um minuto',
            mm : '%d minutos',
            h : 'uma hora',
            hh : '%d horas',
            d : 'um dia',
            dd : '%d dias',
            M : 'um mês',
            MM : '%d meses',
            y : 'um ano',
            yy : '%d anos'
        },
        ordinalParse: /\d{1,2}º/,
        ordinal : '%dº'
    });

    return pt_br;

}));
/* angular-moment.js / v0.10.3 / (c) 2013, 2014, 2015 Uri Shaked / MIT Licence */

'format amd';
/* global define */

(function () {
	'use strict';

	function angularMoment(angular, moment) {

		/**
		 * @ngdoc overview
		 * @name angularMoment
		 *
		 * @description
		 * angularMoment module provides moment.js functionality for angular.js apps.
		 */
		return angular.module('angularMoment', [])

		/**
		 * @ngdoc object
		 * @name angularMoment.config:angularMomentConfig
		 *
		 * @description
		 * Common configuration of the angularMoment module
		 */
			.constant('angularMomentConfig', {
				/**
				 * @ngdoc property
				 * @name angularMoment.config.angularMomentConfig#preprocess
				 * @propertyOf angularMoment.config:angularMomentConfig
				 * @returns {string} The default preprocessor to apply
				 *
				 * @description
				 * Defines a default preprocessor to apply (e.g. 'unix', 'etc', ...). The default value is null,
				 * i.e. no preprocessor will be applied.
				 */
				preprocess: null, // e.g. 'unix', 'utc', ...

				/**
				 * @ngdoc property
				 * @name angularMoment.config.angularMomentConfig#timezone
				 * @propertyOf angularMoment.config:angularMomentConfig
				 * @returns {string} The default timezone
				 *
				 * @description
				 * The default timezone (e.g. 'Europe/London'). Empty string by default (does not apply
				 * any timezone shift).
				 */
				timezone: '',

				/**
				 * @ngdoc property
				 * @name angularMoment.config.angularMomentConfig#format
				 * @propertyOf angularMoment.config:angularMomentConfig
				 * @returns {string} The pre-conversion format of the date
				 *
				 * @description
				 * Specify the format of the input date. Essentially it's a
				 * default and saves you from specifying a format in every
				 * element. Overridden by element attr. Null by default.
				 */
				format: null,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.angularMomentConfig#statefulFilters
				 * @propertyOf angularMoment.config:angularMomentConfig
				 * @returns {boolean} Whether angular-moment filters should be stateless (or not)
				 *
				 * @description
				 * Specifies whether the filters included with angular-moment are stateful.
				 * Stateful filters will automatically re-evaluate whenever you change the timezone
				 * or language settings, but may negatively impact performance. true by default.
				 */
				statefulFilters: true
			})

		/**
		 * @ngdoc object
		 * @name angularMoment.object:moment
		 *
		 * @description
		 * moment global (as provided by the moment.js library)
		 */
			.constant('moment', moment)

		/**
		 * @ngdoc object
		 * @name angularMoment.config:amTimeAgoConfig
		 * @module angularMoment
		 *
		 * @description
		 * configuration specific to the amTimeAgo directive
		 */
			.constant('amTimeAgoConfig', {
				/**
				 * @ngdoc property
				 * @name angularMoment.config.amTimeAgoConfig#withoutSuffix
				 * @propertyOf angularMoment.config:amTimeAgoConfig
				 * @returns {boolean} Whether to include a suffix in am-time-ago directive
				 *
				 * @description
				 * Defaults to false.
				 */
				withoutSuffix: false,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.amTimeAgoConfig#serverTime
				 * @propertyOf angularMoment.config:amTimeAgoConfig
				 * @returns {number} Server time in milliseconds since the epoch
				 *
				 * @description
				 * If set, time ago will be calculated relative to the given value.
				 * If null, local time will be used. Defaults to null.
				 */
				serverTime: null,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.amTimeAgoConfig#titleFormat
				 * @propertyOf angularMoment.config:amTimeAgoConfig
				 * @returns {string} The format of the date to be displayed in the title of the element. If null,
				 *        the directive set the title of the element.
				 *
				 * @description
				 * The format of the date used for the title of the element. null by default.
				 */
				titleFormat: null,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.amTimeAgoConfig#fullDateThreshold
				 * @propertyOf angularMoment.config:amTimeAgoConfig
				 * @returns {number} The minimum number of days for showing a full date instead of relative time
				 *
				 * @description
				 * The threshold for displaying a full date. The default is null, which means the date will always
				 * be relative, and full date will never be displayed.
				 */
				fullDateThreshold: null,

				/**
				 * @ngdoc property
				 * @name angularMoment.config.amTimeAgoConfig#fullDateFormat
				 * @propertyOf angularMoment.config:amTimeAgoConfig
				 * @returns {string} The format to use when displaying a full date.
				 *
				 * @description
				 * Specify the format of the date when displayed as full date. null by default.
				 */
				fullDateFormat: null
			})

		/**
		 * @ngdoc directive
		 * @name angularMoment.directive:amTimeAgo
		 * @module angularMoment
		 *
		 * @restrict A
		 */
			.directive('amTimeAgo', ['$window', 'moment', 'amMoment', 'amTimeAgoConfig', 'angularMomentConfig', function ($window, moment, amMoment, amTimeAgoConfig, angularMomentConfig) {

				return function (scope, element, attr) {
					var activeTimeout = null;
					var currentValue;
					var currentFormat = angularMomentConfig.format;
					var withoutSuffix = amTimeAgoConfig.withoutSuffix;
					var titleFormat = amTimeAgoConfig.titleFormat;
					var fullDateThreshold = amTimeAgoConfig.fullDateThreshold;
					var fullDateFormat = amTimeAgoConfig.fullDateFormat;
					var localDate = new Date().getTime();
					var preprocess = angularMomentConfig.preprocess;
					var modelName = attr.amTimeAgo;
					var currentFrom;
					var isTimeElement = ('TIME' === element[0].nodeName.toUpperCase());

					function getNow() {
						var now;
						if (currentFrom) {
							now = currentFrom;
						} else if (amTimeAgoConfig.serverTime) {
							var localNow = new Date().getTime();
							var nowMillis = localNow - localDate + amTimeAgoConfig.serverTime;
							now = moment(nowMillis);
						}
						else {
							now = moment();
						}
						return now;
					}

					function cancelTimer() {
						if (activeTimeout) {
							$window.clearTimeout(activeTimeout);
							activeTimeout = null;
						}
					}

					function updateTime(momentInstance) {
						var daysAgo = getNow().diff(momentInstance, 'day');
						var showFullDate = fullDateThreshold && daysAgo >= fullDateThreshold;

						if (showFullDate) {
							element.text(momentInstance.format(fullDateFormat));
						} else {
							element.text(momentInstance.from(getNow(), withoutSuffix));
						}

						if (titleFormat && !element.attr('title')) {
							element.attr('title', momentInstance.local().format(titleFormat));
						}

						if (!showFullDate) {
							var howOld = Math.abs(getNow().diff(momentInstance, 'minute'));
							var secondsUntilUpdate = 3600;
							if (howOld < 1) {
								secondsUntilUpdate = 1;
							} else if (howOld < 60) {
								secondsUntilUpdate = 30;
							} else if (howOld < 180) {
								secondsUntilUpdate = 300;
							}

							activeTimeout = $window.setTimeout(function () {
								updateTime(momentInstance);
							}, secondsUntilUpdate * 1000);
						}
					}

					function updateDateTimeAttr(value) {
						if (isTimeElement) {
							element.attr('datetime', value);
						}
					}

					function updateMoment() {
						cancelTimer();
						if (currentValue) {
							var momentValue = amMoment.preprocessDate(currentValue, preprocess, currentFormat);
							updateTime(momentValue);
							updateDateTimeAttr(momentValue.toISOString());
						}
					}

					scope.$watch(modelName, function (value) {
						if ((typeof value === 'undefined') || (value === null) || (value === '')) {
							cancelTimer();
							if (currentValue) {
								element.text('');
								updateDateTimeAttr('');
								currentValue = null;
							}
							return;
						}

						currentValue = value;
						updateMoment();
					});

					if (angular.isDefined(attr.amFrom)) {
						scope.$watch(attr.amFrom, function (value) {
							if ((typeof value === 'undefined') || (value === null) || (value === '')) {
								currentFrom = null;
							} else {
								currentFrom = moment(value);
							}
							updateMoment();
						});
					}

					if (angular.isDefined(attr.amWithoutSuffix)) {
						scope.$watch(attr.amWithoutSuffix, function (value) {
							if (typeof value === 'boolean') {
								withoutSuffix = value;
								updateMoment();
							} else {
								withoutSuffix = amTimeAgoConfig.withoutSuffix;
							}
						});
					}

					attr.$observe('amFormat', function (format) {
						if (typeof format !== 'undefined') {
							currentFormat = format;
							updateMoment();
						}
					});

					attr.$observe('amPreprocess', function (newValue) {
						preprocess = newValue;
						updateMoment();
					});

					attr.$observe('amFullDateThreshold', function (newValue) {
						fullDateThreshold = newValue;
						updateMoment();
					});

					attr.$observe('amFullDateFormat', function (newValue) {
						fullDateFormat = newValue;
						updateMoment();
					});

					scope.$on('$destroy', function () {
						cancelTimer();
					});

					scope.$on('amMoment:localeChanged', function () {
						updateMoment();
					});
				};
			}])

		/**
		 * @ngdoc service
		 * @name angularMoment.service.amMoment
		 * @module angularMoment
		 */
			.service('amMoment', ['moment', '$rootScope', '$log', 'angularMomentConfig', function (moment, $rootScope, $log, angularMomentConfig) {
				/**
				 * @ngdoc property
				 * @name angularMoment:amMoment#preprocessors
				 * @module angularMoment
				 *
				 * @description
				 * Defines the preprocessors for the preprocessDate method. By default, the following preprocessors
				 * are defined: utc, unix.
				 */
				this.preprocessors = {
					utc: moment.utc,
					unix: moment.unix
				};

				/**
				 * @ngdoc function
				 * @name angularMoment.service.amMoment#changeLocale
				 * @methodOf angularMoment.service.amMoment
				 *
				 * @description
				 * Changes the locale for moment.js and updates all the am-time-ago directive instances
				 * with the new locale. Also broadcasts an `amMoment:localeChanged` event on $rootScope.
				 *
				 * @param {string} locale Locale code (e.g. en, es, ru, pt-br, etc.)
				 * @param {object} customization object of locale strings to override
				 */
				this.changeLocale = function (locale, customization) {
					var result = moment.locale(locale, customization);
					if (angular.isDefined(locale)) {
						$rootScope.$broadcast('amMoment:localeChanged');

					}
					return result;
				};

				/**
				 * @ngdoc function
				 * @name angularMoment.service.amMoment#changeTimezone
				 * @methodOf angularMoment.service.amMoment
				 *
				 * @description
				 * Changes the default timezone for amCalendar, amDateFormat and amTimeAgo. Also broadcasts an
				 * `amMoment:timezoneChanged` event on $rootScope.
				 *
				 * @param {string} timezone Timezone name (e.g. UTC)
				 */
				this.changeTimezone = function (timezone) {
					angularMomentConfig.timezone = timezone;
					$rootScope.$broadcast('amMoment:timezoneChanged');
				};

				/**
				 * @ngdoc function
				 * @name angularMoment.service.amMoment#preprocessDate
				 * @methodOf angularMoment.service.amMoment
				 *
				 * @description
				 * Preprocess a given value and convert it into a Moment instance appropriate for use in the
				 * am-time-ago directive and the filters.
				 *
				 * @param {*} value The value to be preprocessed
				 * @param {string} preprocess The name of the preprocessor the apply (e.g. utc, unix)
				 * @param {string=} format Specifies how to parse the value (see {@link http://momentjs.com/docs/#/parsing/string-format/})
				 * @return {Moment} A value that can be parsed by the moment library
				 */
				this.preprocessDate = function (value, preprocess, format) {
					if (angular.isUndefined(preprocess)) {
						preprocess = angularMomentConfig.preprocess;
					}
					if (this.preprocessors[preprocess]) {
						return this.preprocessors[preprocess](value, format);
					}
					if (preprocess) {
						$log.warn('angular-moment: Ignoring unsupported value for preprocess: ' + preprocess);
					}
					if (!isNaN(parseFloat(value)) && isFinite(value)) {
						// Milliseconds since the epoch
						return moment(parseInt(value, 10));
					}
					// else just returns the value as-is.
					return moment(value, format);
				};

				/**
				 * @ngdoc function
				 * @name angularMoment.service.amMoment#applyTimezone
				 * @methodOf angularMoment.service.amMoment
				 *
				 * @description
				 * Apply a timezone onto a given moment object. It can be a named timezone (e.g. 'America/Phoenix') or an offset from UTC (e.g. '+0300')
				 * moment-timezone.js is needed when a named timezone is used, otherwise, it'll not apply any timezone shift.
				 *
				 * @param {Moment} aMoment a moment() instance to apply the timezone shift to
				 * @param {string=} timezone The timezone to apply. If none given, will apply the timezone
				 *        configured in angularMomentConfig.timezone. It can be a named timezone (e.g. 'America/Phoenix') or an offset from UTC (e.g. '+0300')
				 *
				 * @returns {Moment} The given moment with the timezone shift applied
				 */
				this.applyTimezone = function (aMoment, timezone) {
					timezone = timezone || angularMomentConfig.timezone;
					if (!timezone) {
						return aMoment;
					}

					if (timezone.match(/^Z|[+-]\d\d:?\d\d$/i)) {
						aMoment = aMoment.utcOffset(timezone);
					} else if (aMoment.tz) {
						aMoment = aMoment.tz(timezone);
					} else {
						$log.warn('angular-moment: named timezone specified but moment.tz() is undefined. Did you forget to include moment-timezone.js?');
					}

					return aMoment;
				};
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amCalendar
		 * @module angularMoment
		 */
			.filter('amCalendar', ['moment', 'amMoment', 'angularMomentConfig', function (moment, amMoment, angularMomentConfig) {
				function amCalendarFilter(value, preprocess, timezone) {
					if (typeof value === 'undefined' || value === null) {
						return '';
					}

					value = amMoment.preprocessDate(value, preprocess);
					var date = moment(value);
					if (!date.isValid()) {
						return '';
					}

					return amMoment.applyTimezone(date, timezone).calendar();
				}

				// Since AngularJS 1.3, filters have to explicitly define being stateful
				// (this is no longer the default).
				amCalendarFilter.$stateful = angularMomentConfig.statefulFilters;

				return amCalendarFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amDifference
		 * @module angularMoment
		 */
			.filter('amDifference', ['moment', 'amMoment', 'angularMomentConfig', function (moment, amMoment, angularMomentConfig) {
				function amDifferenceFilter(value, otherValue, unit, usePrecision, preprocessValue, preprocessOtherValue) {
					if (typeof value === 'undefined' || value === null) {
						return '';
					}

					value = amMoment.preprocessDate(value, preprocessValue);
					var date = moment(value);
					if (!date.isValid()) {
						return '';
					}

					var date2;
					if (typeof otherValue === 'undefined' || otherValue === null) {
						date2 = moment();
					} else {
						otherValue = amMoment.preprocessDate(otherValue, preprocessOtherValue);
						date2 = moment(otherValue);
						if (!date2.isValid()) {
							return '';
						}
					}

					return amMoment.applyTimezone(date).diff(amMoment.applyTimezone(date2), unit, usePrecision);
				}

				amDifferenceFilter.$stateful = angularMomentConfig.statefulFilters;

				return amDifferenceFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amDateFormat
		 * @module angularMoment
		 * @function
		 */
			.filter('amDateFormat', ['moment', 'amMoment', 'angularMomentConfig', function (moment, amMoment, angularMomentConfig) {
				function amDateFormatFilter(value, format, preprocess, timezone, inputFormat) {
					var currentFormat = inputFormat || angularMomentConfig.format;
					if (typeof value === 'undefined' || value === null) {
						return '';
					}

					value = amMoment.preprocessDate(value, preprocess, currentFormat);
					var date = moment(value);
					if (!date.isValid()) {
						return '';
					}

					return amMoment.applyTimezone(date, timezone).format(format);
				}

				amDateFormatFilter.$stateful = angularMomentConfig.statefulFilters;

				return amDateFormatFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amDurationFormat
		 * @module angularMoment
		 * @function
		 */
			.filter('amDurationFormat', ['moment', 'angularMomentConfig', function (moment, angularMomentConfig) {
				function amDurationFormatFilter(value, format, suffix) {
					if (typeof value === 'undefined' || value === null) {
						return '';
					}

					return moment.duration(value, format).humanize(suffix);
				}

				amDurationFormatFilter.$stateful = angularMomentConfig.statefulFilters;

				return amDurationFormatFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amTimeAgo
		 * @module angularMoment
		 * @function
		 */
			.filter('amTimeAgo', ['moment', 'amMoment', 'angularMomentConfig', function (moment, amMoment, angularMomentConfig) {
				function amTimeAgoFilter(value, preprocess, suffix, from) {
					var date, dateFrom;

					if (typeof value === 'undefined' || value === null) {
						return '';
					}

					value = amMoment.preprocessDate(value, preprocess);
					date = moment(value);
					if (!date.isValid()) {
						return '';
					}

					dateFrom = moment(from);
					if (typeof from !== 'undefined' && dateFrom.isValid()) {
						return amMoment.applyTimezone(date).from(dateFrom, suffix);
					}

					return amMoment.applyTimezone(date).fromNow(suffix);
				}

				amTimeAgoFilter.$stateful = angularMomentConfig.statefulFilters;

				return amTimeAgoFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amSubtract
		 * @module angularMoment
		 * @function
		 */
			.filter('amSubtract', ['moment', 'angularMomentConfig', function (moment, angularMomentConfig) {
				function amSubtractFilter(value, amount, type) {

					if (typeof value === 'undefined' || value === null) {
						return '';
					}

					return moment(value).subtract(parseInt(amount, 10), type);
				}

				amSubtractFilter.$stateful = angularMomentConfig.statefulFilters;

				return amSubtractFilter;
			}])

		/**
		 * @ngdoc filter
		 * @name angularMoment.filter:amAdd
		 * @module angularMoment
		 * @function
		 */
			.filter('amAdd', ['moment', 'angularMomentConfig', function (moment, angularMomentConfig) {
				function amAddFilter(value, amount, type) {

					if (typeof value === 'undefined' || value === null) {
						return '';
					}

					return moment(value).add(parseInt(amount, 10), type);
				}

				amAddFilter.$stateful = angularMomentConfig.statefulFilters;

				return amAddFilter;
			}]);
	}

	if (typeof define === 'function' && define.amd) {
		define(['angular', 'moment'], angularMoment);
	} else if (typeof module !== 'undefined' && module && module.exports) {
		angularMoment(angular, require('moment'));
		module.exports = 'angularMoment';
	} else {
		angularMoment(angular, (typeof global !== 'undefined' ? global : window).moment);
	}
})();

/**
 * @license AngularJS v1.6.3
 * (c) 2010-2017 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

var $resourceMinErr = angular.$$minErr('$resource');

// Helper functions and regex to lookup a dotted path on an object
// stopping at undefined/null.  The path must be composed of ASCII
// identifiers (just like $parse)
var MEMBER_NAME_REGEX = /^(\.[a-zA-Z_$@][0-9a-zA-Z_$@]*)+$/;

function isValidDottedPath(path) {
  return (path != null && path !== '' && path !== 'hasOwnProperty' &&
      MEMBER_NAME_REGEX.test('.' + path));
}

function lookupDottedPath(obj, path) {
  if (!isValidDottedPath(path)) {
    throw $resourceMinErr('badmember', 'Dotted member path "@{0}" is invalid.', path);
  }
  var keys = path.split('.');
  for (var i = 0, ii = keys.length; i < ii && angular.isDefined(obj); i++) {
    var key = keys[i];
    obj = (obj !== null) ? obj[key] : undefined;
  }
  return obj;
}

/**
 * Create a shallow copy of an object and clear other fields from the destination
 */
function shallowClearAndCopy(src, dst) {
  dst = dst || {};

  angular.forEach(dst, function(value, key) {
    delete dst[key];
  });

  for (var key in src) {
    if (src.hasOwnProperty(key) && !(key.charAt(0) === '$' && key.charAt(1) === '$')) {
      dst[key] = src[key];
    }
  }

  return dst;
}

/**
 * @ngdoc module
 * @name ngResource
 * @description
 *
 * # ngResource
 *
 * The `ngResource` module provides interaction support with RESTful services
 * via the $resource service.
 *
 *
 * <div doc-module-components="ngResource"></div>
 *
 * See {@link ngResource.$resourceProvider} and {@link ngResource.$resource} for usage.
 */

/**
 * @ngdoc provider
 * @name $resourceProvider
 *
 * @description
 *
 * Use `$resourceProvider` to change the default behavior of the {@link ngResource.$resource}
 * service.
 *
 * ## Dependencies
 * Requires the {@link ngResource } module to be installed.
 *
 */

/**
 * @ngdoc service
 * @name $resource
 * @requires $http
 * @requires ng.$log
 * @requires $q
 * @requires ng.$timeout
 *
 * @description
 * A factory which creates a resource object that lets you interact with
 * [RESTful](http://en.wikipedia.org/wiki/Representational_State_Transfer) server-side data sources.
 *
 * The returned resource object has action methods which provide high-level behaviors without
 * the need to interact with the low level {@link ng.$http $http} service.
 *
 * Requires the {@link ngResource `ngResource`} module to be installed.
 *
 * By default, trailing slashes will be stripped from the calculated URLs,
 * which can pose problems with server backends that do not expect that
 * behavior.  This can be disabled by configuring the `$resourceProvider` like
 * this:
 *
 * ```js
     app.config(['$resourceProvider', function($resourceProvider) {
       // Don't strip trailing slashes from calculated URLs
       $resourceProvider.defaults.stripTrailingSlashes = false;
     }]);
 * ```
 *
 * @param {string} url A parameterized URL template with parameters prefixed by `:` as in
 *   `/user/:username`. If you are using a URL with a port number (e.g.
 *   `http://example.com:8080/api`), it will be respected.
 *
 *   If you are using a url with a suffix, just add the suffix, like this:
 *   `$resource('http://example.com/resource.json')` or `$resource('http://example.com/:id.json')`
 *   or even `$resource('http://example.com/resource/:resource_id.:format')`
 *   If the parameter before the suffix is empty, :resource_id in this case, then the `/.` will be
 *   collapsed down to a single `.`.  If you need this sequence to appear and not collapse then you
 *   can escape it with `/\.`.
 *
 * @param {Object=} paramDefaults Default values for `url` parameters. These can be overridden in
 *   `actions` methods. If a parameter value is a function, it will be called every time
 *   a param value needs to be obtained for a request (unless the param was overridden). The function
 *   will be passed the current data value as an argument.
 *
 *   Each key value in the parameter object is first bound to url template if present and then any
 *   excess keys are appended to the url search query after the `?`.
 *
 *   Given a template `/path/:verb` and parameter `{verb:'greet', salutation:'Hello'}` results in
 *   URL `/path/greet?salutation=Hello`.
 *
 *   If the parameter value is prefixed with `@`, then the value for that parameter will be
 *   extracted from the corresponding property on the `data` object (provided when calling a
 *   "non-GET" action method).
 *   For example, if the `defaultParam` object is `{someParam: '@someProp'}` then the value of
 *   `someParam` will be `data.someProp`.
 *   Note that the parameter will be ignored, when calling a "GET" action method (i.e. an action
 *   method that does not accept a request body)
 *
 * @param {Object.<Object>=} actions Hash with declaration of custom actions that will be available
 *   in addition to the default set of resource actions (see below). If a custom action has the same
 *   key as a default action (e.g. `save`), then the default action will be *overwritten*, and not
 *   extended.
 *
 *   The declaration should be created in the format of {@link ng.$http#usage $http.config}:
 *
 *       {action1: {method:?, params:?, isArray:?, headers:?, ...},
 *        action2: {method:?, params:?, isArray:?, headers:?, ...},
 *        ...}
 *
 *   Where:
 *
 *   - **`action`** – {string} – The name of action. This name becomes the name of the method on
 *     your resource object.
 *   - **`method`** – {string} – Case insensitive HTTP method (e.g. `GET`, `POST`, `PUT`,
 *     `DELETE`, `JSONP`, etc).
 *   - **`params`** – {Object=} – Optional set of pre-bound parameters for this action. If any of
 *     the parameter value is a function, it will be called every time when a param value needs to
 *     be obtained for a request (unless the param was overridden). The function will be passed the
 *     current data value as an argument.
 *   - **`url`** – {string} – action specific `url` override. The url templating is supported just
 *     like for the resource-level urls.
 *   - **`isArray`** – {boolean=} – If true then the returned object for this action is an array,
 *     see `returns` section.
 *   - **`transformRequest`** –
 *     `{function(data, headersGetter)|Array.<function(data, headersGetter)>}` –
 *     transform function or an array of such functions. The transform function takes the http
 *     request body and headers and returns its transformed (typically serialized) version.
 *     By default, transformRequest will contain one function that checks if the request data is
 *     an object and serializes it using `angular.toJson`. To prevent this behavior, set
 *     `transformRequest` to an empty array: `transformRequest: []`
 *   - **`transformResponse`** –
 *     `{function(data, headersGetter, status)|Array.<function(data, headersGetter, status)>}` –
 *     transform function or an array of such functions. The transform function takes the http
 *     response body, headers and status and returns its transformed (typically deserialized)
 *     version.
 *     By default, transformResponse will contain one function that checks if the response looks
 *     like a JSON string and deserializes it using `angular.fromJson`. To prevent this behavior,
 *     set `transformResponse` to an empty array: `transformResponse: []`
 *   - **`cache`** – `{boolean|Cache}` – If true, a default $http cache will be used to cache the
 *     GET request, otherwise if a cache instance built with
 *     {@link ng.$cacheFactory $cacheFactory} is supplied, this cache will be used for
 *     caching.
 *   - **`timeout`** – `{number}` – timeout in milliseconds.<br />
 *     **Note:** In contrast to {@link ng.$http#usage $http.config}, {@link ng.$q promises} are
 *     **not** supported in $resource, because the same value would be used for multiple requests.
 *     If you are looking for a way to cancel requests, you should use the `cancellable` option.
 *   - **`cancellable`** – `{boolean}` – if set to true, the request made by a "non-instance" call
 *     will be cancelled (if not already completed) by calling `$cancelRequest()` on the call's
 *     return value. Calling `$cancelRequest()` for a non-cancellable or an already
 *     completed/cancelled request will have no effect.<br />
 *   - **`withCredentials`** - `{boolean}` - whether to set the `withCredentials` flag on the
 *     XHR object. See
 *     [requests with credentials](https://developer.mozilla.org/en/http_access_control#section_5)
 *     for more information.
 *   - **`responseType`** - `{string}` - see
 *     [requestType](https://developer.mozilla.org/en-US/docs/DOM/XMLHttpRequest#responseType).
 *   - **`interceptor`** - `{Object=}` - The interceptor object has two optional methods -
 *     `response` and `responseError`. Both `response` and `responseError` interceptors get called
 *     with `http response` object. See {@link ng.$http $http interceptors}.
 *
 * @param {Object} options Hash with custom settings that should extend the
 *   default `$resourceProvider` behavior.  The supported options are:
 *
 *   - **`stripTrailingSlashes`** – {boolean} – If true then the trailing
 *   slashes from any calculated URL will be stripped. (Defaults to true.)
 *   - **`cancellable`** – {boolean} – If true, the request made by a "non-instance" call will be
 *   cancelled (if not already completed) by calling `$cancelRequest()` on the call's return value.
 *   This can be overwritten per action. (Defaults to false.)
 *
 * @returns {Object} A resource "class" object with methods for the default set of resource actions
 *   optionally extended with custom `actions`. The default set contains these actions:
 *   ```js
 *   { 'get':    {method:'GET'},
 *     'save':   {method:'POST'},
 *     'query':  {method:'GET', isArray:true},
 *     'remove': {method:'DELETE'},
 *     'delete': {method:'DELETE'} };
 *   ```
 *
 *   Calling these methods invoke an {@link ng.$http} with the specified http method,
 *   destination and parameters. When the data is returned from the server then the object is an
 *   instance of the resource class. The actions `save`, `remove` and `delete` are available on it
 *   as  methods with the `$` prefix. This allows you to easily perform CRUD operations (create,
 *   read, update, delete) on server-side data like this:
 *   ```js
 *   var User = $resource('/user/:userId', {userId:'@id'});
 *   var user = User.get({userId:123}, function() {
 *     user.abc = true;
 *     user.$save();
 *   });
 *   ```
 *
 *   It is important to realize that invoking a $resource object method immediately returns an
 *   empty reference (object or array depending on `isArray`). Once the data is returned from the
 *   server the existing reference is populated with the actual data. This is a useful trick since
 *   usually the resource is assigned to a model which is then rendered by the view. Having an empty
 *   object results in no rendering, once the data arrives from the server then the object is
 *   populated with the data and the view automatically re-renders itself showing the new data. This
 *   means that in most cases one never has to write a callback function for the action methods.
 *
 *   The action methods on the class object or instance object can be invoked with the following
 *   parameters:
 *
 *   - HTTP GET "class" actions: `Resource.action([parameters], [success], [error])`
 *   - non-GET "class" actions: `Resource.action([parameters], postData, [success], [error])`
 *   - non-GET instance actions:  `instance.$action([parameters], [success], [error])`
 *
 *
 *   Success callback is called with (value (Object|Array), responseHeaders (Function),
 *   status (number), statusText (string)) arguments, where the value is the populated resource
 *   instance or collection object. The error callback is called with (httpResponse) argument.
 *
 *   Class actions return empty instance (with additional properties below).
 *   Instance actions return promise of the action.
 *
 *   The Resource instances and collections have these additional properties:
 *
 *   - `$promise`: the {@link ng.$q promise} of the original server interaction that created this
 *     instance or collection.
 *
 *     On success, the promise is resolved with the same resource instance or collection object,
 *     updated with data from server. This makes it easy to use in
 *     {@link ngRoute.$routeProvider resolve section of $routeProvider.when()} to defer view
 *     rendering until the resource(s) are loaded.
 *
 *     On failure, the promise is rejected with the {@link ng.$http http response} object, without
 *     the `resource` property.
 *
 *     If an interceptor object was provided, the promise will instead be resolved with the value
 *     returned by the interceptor.
 *
 *   - `$resolved`: `true` after first server interaction is completed (either with success or
 *      rejection), `false` before that. Knowing if the Resource has been resolved is useful in
 *      data-binding.
 *
 *   The Resource instances and collections have these additional methods:
 *
 *   - `$cancelRequest`: If there is a cancellable, pending request related to the instance or
 *      collection, calling this method will abort the request.
 *
 *   The Resource instances have these additional methods:
 *
 *   - `toJSON`: It returns a simple object without any of the extra properties added as part of
 *     the Resource API. This object can be serialized through {@link angular.toJson} safely
 *     without attaching Angular-specific fields. Notice that `JSON.stringify` (and
 *     `angular.toJson`) automatically use this method when serializing a Resource instance
 *     (see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#toJSON()_behavior)).
 *
 * @example
 *
 * # Credit card resource
 *
 * ```js
     // Define CreditCard class
     var CreditCard = $resource('/user/:userId/card/:cardId',
      {userId:123, cardId:'@id'}, {
       charge: {method:'POST', params:{charge:true}}
      });

     // We can retrieve a collection from the server
     var cards = CreditCard.query(function() {
       // GET: /user/123/card
       // server returns: [ {id:456, number:'1234', name:'Smith'} ];

       var card = cards[0];
       // each item is an instance of CreditCard
       expect(card instanceof CreditCard).toEqual(true);
       card.name = "J. Smith";
       // non GET methods are mapped onto the instances
       card.$save();
       // POST: /user/123/card/456 {id:456, number:'1234', name:'J. Smith'}
       // server returns: {id:456, number:'1234', name: 'J. Smith'};

       // our custom method is mapped as well.
       card.$charge({amount:9.99});
       // POST: /user/123/card/456?amount=9.99&charge=true {id:456, number:'1234', name:'J. Smith'}
     });

     // we can create an instance as well
     var newCard = new CreditCard({number:'0123'});
     newCard.name = "Mike Smith";
     newCard.$save();
     // POST: /user/123/card {number:'0123', name:'Mike Smith'}
     // server returns: {id:789, number:'0123', name: 'Mike Smith'};
     expect(newCard.id).toEqual(789);
 * ```
 *
 * The object returned from this function execution is a resource "class" which has "static" method
 * for each action in the definition.
 *
 * Calling these methods invoke `$http` on the `url` template with the given `method`, `params` and
 * `headers`.
 *
 * @example
 *
 * # User resource
 *
 * When the data is returned from the server then the object is an instance of the resource type and
 * all of the non-GET methods are available with `$` prefix. This allows you to easily support CRUD
 * operations (create, read, update, delete) on server-side data.

   ```js
     var User = $resource('/user/:userId', {userId:'@id'});
     User.get({userId:123}, function(user) {
       user.abc = true;
       user.$save();
     });
   ```
 *
 * It's worth noting that the success callback for `get`, `query` and other methods gets passed
 * in the response that came from the server as well as $http header getter function, so one
 * could rewrite the above example and get access to http headers as:
 *
   ```js
     var User = $resource('/user/:userId', {userId:'@id'});
     User.get({userId:123}, function(user, getResponseHeaders){
       user.abc = true;
       user.$save(function(user, putResponseHeaders) {
         //user => saved user object
         //putResponseHeaders => $http header getter
       });
     });
   ```
 *
 * You can also access the raw `$http` promise via the `$promise` property on the object returned
 *
   ```
     var User = $resource('/user/:userId', {userId:'@id'});
     User.get({userId:123})
         .$promise.then(function(user) {
           $scope.user = user;
         });
   ```
 *
 * @example
 *
 * # Creating a custom 'PUT' request
 *
 * In this example we create a custom method on our resource to make a PUT request
 * ```js
 *    var app = angular.module('app', ['ngResource', 'ngRoute']);
 *
 *    // Some APIs expect a PUT request in the format URL/object/ID
 *    // Here we are creating an 'update' method
 *    app.factory('Notes', ['$resource', function($resource) {
 *    return $resource('/notes/:id', null,
 *        {
 *            'update': { method:'PUT' }
 *        });
 *    }]);
 *
 *    // In our controller we get the ID from the URL using ngRoute and $routeParams
 *    // We pass in $routeParams and our Notes factory along with $scope
 *    app.controller('NotesCtrl', ['$scope', '$routeParams', 'Notes',
                                      function($scope, $routeParams, Notes) {
 *    // First get a note object from the factory
 *    var note = Notes.get({ id:$routeParams.id });
 *    $id = note.id;
 *
 *    // Now call update passing in the ID first then the object you are updating
 *    Notes.update({ id:$id }, note);
 *
 *    // This will PUT /notes/ID with the note object in the request payload
 *    }]);
 * ```
 *
 * @example
 *
 * # Cancelling requests
 *
 * If an action's configuration specifies that it is cancellable, you can cancel the request related
 * to an instance or collection (as long as it is a result of a "non-instance" call):
 *
   ```js
     // ...defining the `Hotel` resource...
     var Hotel = $resource('/api/hotel/:id', {id: '@id'}, {
       // Let's make the `query()` method cancellable
       query: {method: 'get', isArray: true, cancellable: true}
     });

     // ...somewhere in the PlanVacationController...
     ...
     this.onDestinationChanged = function onDestinationChanged(destination) {
       // We don't care about any pending request for hotels
       // in a different destination any more
       this.availableHotels.$cancelRequest();

       // Let's query for hotels in '<destination>'
       // (calls: /api/hotel?location=<destination>)
       this.availableHotels = Hotel.query({location: destination});
     };
   ```
 *
 */
angular.module('ngResource', ['ng']).
  info({ angularVersion: '1.6.3' }).
  provider('$resource', function ResourceProvider() {
    var PROTOCOL_AND_IPV6_REGEX = /^https?:\/\/\[[^\]]*][^/]*/;

    var provider = this;

    /**
     * @ngdoc property
     * @name $resourceProvider#defaults
     * @description
     * Object containing default options used when creating `$resource` instances.
     *
     * The default values satisfy a wide range of usecases, but you may choose to overwrite any of
     * them to further customize your instances. The available properties are:
     *
     * - **stripTrailingSlashes** – `{boolean}` – If true, then the trailing slashes from any
     *   calculated URL will be stripped.<br />
     *   (Defaults to true.)
     * - **cancellable** – `{boolean}` – If true, the request made by a "non-instance" call will be
     *   cancelled (if not already completed) by calling `$cancelRequest()` on the call's return
     *   value. For more details, see {@link ngResource.$resource}. This can be overwritten per
     *   resource class or action.<br />
     *   (Defaults to false.)
     * - **actions** - `{Object.<Object>}` - A hash with default actions declarations. Actions are
     *   high-level methods corresponding to RESTful actions/methods on resources. An action may
     *   specify what HTTP method to use, what URL to hit, if the return value will be a single
     *   object or a collection (array) of objects etc. For more details, see
     *   {@link ngResource.$resource}. The actions can also be enhanced or overwritten per resource
     *   class.<br />
     *   The default actions are:
     *   ```js
     *   {
     *     get: {method: 'GET'},
     *     save: {method: 'POST'},
     *     query: {method: 'GET', isArray: true},
     *     remove: {method: 'DELETE'},
     *     delete: {method: 'DELETE'}
     *   }
     *   ```
     *
     * #### Example
     *
     * For example, you can specify a new `update` action that uses the `PUT` HTTP verb:
     *
     * ```js
     *   angular.
     *     module('myApp').
     *     config(['$resourceProvider', function ($resourceProvider) {
     *       $resourceProvider.defaults.actions.update = {
     *         method: 'PUT'
     *       };
     *     });
     * ```
     *
     * Or you can even overwrite the whole `actions` list and specify your own:
     *
     * ```js
     *   angular.
     *     module('myApp').
     *     config(['$resourceProvider', function ($resourceProvider) {
     *       $resourceProvider.defaults.actions = {
     *         create: {method: 'POST'},
     *         get:    {method: 'GET'},
     *         getAll: {method: 'GET', isArray:true},
     *         update: {method: 'PUT'},
     *         delete: {method: 'DELETE'}
     *       };
     *     });
     * ```
     *
     */
    this.defaults = {
      // Strip slashes by default
      stripTrailingSlashes: true,

      // Make non-instance requests cancellable (via `$cancelRequest()`)
      cancellable: false,

      // Default actions configuration
      actions: {
        'get': {method: 'GET'},
        'save': {method: 'POST'},
        'query': {method: 'GET', isArray: true},
        'remove': {method: 'DELETE'},
        'delete': {method: 'DELETE'}
      }
    };

    this.$get = ['$http', '$log', '$q', '$timeout', function($http, $log, $q, $timeout) {

      var noop = angular.noop,
          forEach = angular.forEach,
          extend = angular.extend,
          copy = angular.copy,
          isArray = angular.isArray,
          isDefined = angular.isDefined,
          isFunction = angular.isFunction,
          isNumber = angular.isNumber,
          encodeUriQuery = angular.$$encodeUriQuery,
          encodeUriSegment = angular.$$encodeUriSegment;

      function Route(template, defaults) {
        this.template = template;
        this.defaults = extend({}, provider.defaults, defaults);
        this.urlParams = {};
      }

      Route.prototype = {
        setUrlParams: function(config, params, actionUrl) {
          var self = this,
            url = actionUrl || self.template,
            val,
            encodedVal,
            protocolAndIpv6 = '';

          var urlParams = self.urlParams = Object.create(null);
          forEach(url.split(/\W/), function(param) {
            if (param === 'hasOwnProperty') {
              throw $resourceMinErr('badname', 'hasOwnProperty is not a valid parameter name.');
            }
            if (!(new RegExp('^\\d+$').test(param)) && param &&
              (new RegExp('(^|[^\\\\]):' + param + '(\\W|$)').test(url))) {
              urlParams[param] = {
                isQueryParamValue: (new RegExp('\\?.*=:' + param + '(?:\\W|$)')).test(url)
              };
            }
          });
          url = url.replace(/\\:/g, ':');
          url = url.replace(PROTOCOL_AND_IPV6_REGEX, function(match) {
            protocolAndIpv6 = match;
            return '';
          });

          params = params || {};
          forEach(self.urlParams, function(paramInfo, urlParam) {
            val = params.hasOwnProperty(urlParam) ? params[urlParam] : self.defaults[urlParam];
            if (isDefined(val) && val !== null) {
              if (paramInfo.isQueryParamValue) {
                encodedVal = encodeUriQuery(val, true);
              } else {
                encodedVal = encodeUriSegment(val);
              }
              url = url.replace(new RegExp(':' + urlParam + '(\\W|$)', 'g'), function(match, p1) {
                return encodedVal + p1;
              });
            } else {
              url = url.replace(new RegExp('(/?):' + urlParam + '(\\W|$)', 'g'), function(match,
                  leadingSlashes, tail) {
                if (tail.charAt(0) === '/') {
                  return tail;
                } else {
                  return leadingSlashes + tail;
                }
              });
            }
          });

          // strip trailing slashes and set the url (unless this behavior is specifically disabled)
          if (self.defaults.stripTrailingSlashes) {
            url = url.replace(/\/+$/, '') || '/';
          }

          // Collapse `/.` if found in the last URL path segment before the query.
          // E.g. `http://url.com/id/.format?q=x` becomes `http://url.com/id.format?q=x`.
          url = url.replace(/\/\.(?=\w+($|\?))/, '.');
          // Replace escaped `/\.` with `/.`.
          // (If `\.` comes from a param value, it will be encoded as `%5C.`.)
          config.url = protocolAndIpv6 + url.replace(/\/(\\|%5C)\./, '/.');


          // set params - delegate param encoding to $http
          forEach(params, function(value, key) {
            if (!self.urlParams[key]) {
              config.params = config.params || {};
              config.params[key] = value;
            }
          });
        }
      };


      function resourceFactory(url, paramDefaults, actions, options) {
        var route = new Route(url, options);

        actions = extend({}, provider.defaults.actions, actions);

        function extractParams(data, actionParams) {
          var ids = {};
          actionParams = extend({}, paramDefaults, actionParams);
          forEach(actionParams, function(value, key) {
            if (isFunction(value)) { value = value(data); }
            ids[key] = value && value.charAt && value.charAt(0) === '@' ?
              lookupDottedPath(data, value.substr(1)) : value;
          });
          return ids;
        }

        function defaultResponseInterceptor(response) {
          return response.resource;
        }

        function Resource(value) {
          shallowClearAndCopy(value || {}, this);
        }

        Resource.prototype.toJSON = function() {
          var data = extend({}, this);
          delete data.$promise;
          delete data.$resolved;
          delete data.$cancelRequest;
          return data;
        };

        forEach(actions, function(action, name) {
          var hasBody = /^(POST|PUT|PATCH)$/i.test(action.method);
          var numericTimeout = action.timeout;
          var cancellable = isDefined(action.cancellable) ?
              action.cancellable : route.defaults.cancellable;

          if (numericTimeout && !isNumber(numericTimeout)) {
            $log.debug('ngResource:\n' +
                       '  Only numeric values are allowed as `timeout`.\n' +
                       '  Promises are not supported in $resource, because the same value would ' +
                       'be used for multiple requests. If you are looking for a way to cancel ' +
                       'requests, you should use the `cancellable` option.');
            delete action.timeout;
            numericTimeout = null;
          }

          Resource[name] = function(a1, a2, a3, a4) {
            var params = {}, data, success, error;

            switch (arguments.length) {
              case 4:
                error = a4;
                success = a3;
                // falls through
              case 3:
              case 2:
                if (isFunction(a2)) {
                  if (isFunction(a1)) {
                    success = a1;
                    error = a2;
                    break;
                  }

                  success = a2;
                  error = a3;
                  // falls through
                } else {
                  params = a1;
                  data = a2;
                  success = a3;
                  break;
                }
                // falls through
              case 1:
                if (isFunction(a1)) success = a1;
                else if (hasBody) data = a1;
                else params = a1;
                break;
              case 0: break;
              default:
                throw $resourceMinErr('badargs',
                  'Expected up to 4 arguments [params, data, success, error], got {0} arguments',
                  arguments.length);
            }

            var isInstanceCall = this instanceof Resource;
            var value = isInstanceCall ? data : (action.isArray ? [] : new Resource(data));
            var httpConfig = {};
            var responseInterceptor = action.interceptor && action.interceptor.response ||
              defaultResponseInterceptor;
            var responseErrorInterceptor = action.interceptor && action.interceptor.responseError ||
              undefined;
            var hasError = !!error;
            var hasResponseErrorInterceptor = !!responseErrorInterceptor;
            var timeoutDeferred;
            var numericTimeoutPromise;

            forEach(action, function(value, key) {
              switch (key) {
                default:
                  httpConfig[key] = copy(value);
                  break;
                case 'params':
                case 'isArray':
                case 'interceptor':
                case 'cancellable':
                  break;
              }
            });

            if (!isInstanceCall && cancellable) {
              timeoutDeferred = $q.defer();
              httpConfig.timeout = timeoutDeferred.promise;

              if (numericTimeout) {
                numericTimeoutPromise = $timeout(timeoutDeferred.resolve, numericTimeout);
              }
            }

            if (hasBody) httpConfig.data = data;
            route.setUrlParams(httpConfig,
              extend({}, extractParams(data, action.params || {}), params),
              action.url);

            var promise = $http(httpConfig).then(function(response) {
              var data = response.data;

              if (data) {
                // Need to convert action.isArray to boolean in case it is undefined
                if (isArray(data) !== (!!action.isArray)) {
                  throw $resourceMinErr('badcfg',
                      'Error in resource configuration for action `{0}`. Expected response to ' +
                      'contain an {1} but got an {2} (Request: {3} {4})', name, action.isArray ? 'array' : 'object',
                    isArray(data) ? 'array' : 'object', httpConfig.method, httpConfig.url);
                }
                if (action.isArray) {
                  value.length = 0;
                  forEach(data, function(item) {
                    if (typeof item === 'object') {
                      value.push(new Resource(item));
                    } else {
                      // Valid JSON values may be string literals, and these should not be converted
                      // into objects. These items will not have access to the Resource prototype
                      // methods, but unfortunately there
                      value.push(item);
                    }
                  });
                } else {
                  var promise = value.$promise;     // Save the promise
                  shallowClearAndCopy(data, value);
                  value.$promise = promise;         // Restore the promise
                }
              }
              response.resource = value;

              return response;
            });

            promise = promise['finally'](function() {
              value.$resolved = true;
              if (!isInstanceCall && cancellable) {
                value.$cancelRequest = noop;
                $timeout.cancel(numericTimeoutPromise);
                timeoutDeferred = numericTimeoutPromise = httpConfig.timeout = null;
              }
            });

            promise = promise.then(
              function(response) {
                var value = responseInterceptor(response);
                (success || noop)(value, response.headers, response.status, response.statusText);
                return value;
              },
              (hasError || hasResponseErrorInterceptor) ?
                function(response) {
                  if (hasError && !hasResponseErrorInterceptor) {
                    // Avoid `Possibly Unhandled Rejection` error,
                    // but still fulfill the returned promise with a rejection
                    promise.catch(noop);
                  }
                  if (hasError) error(response);
                  return hasResponseErrorInterceptor ?
                    responseErrorInterceptor(response) :
                    $q.reject(response);
                } :
                undefined);

            if (!isInstanceCall) {
              // we are creating instance / collection
              // - set the initial promise
              // - return the instance / collection
              value.$promise = promise;
              value.$resolved = false;
              if (cancellable) value.$cancelRequest = cancelRequest;

              return value;
            }

            // instance call
            return promise;

            function cancelRequest(value) {
              promise.catch(noop);
              timeoutDeferred.resolve(value);
            }
          };


          Resource.prototype['$' + name] = function(params, success, error) {
            if (isFunction(params)) {
              error = success; success = params; params = {};
            }
            var result = Resource[name].call(this, params, this, success, error);
            return result.$promise || result;
          };
        });

        Resource.bind = function(additionalParamDefaults) {
          var extendedParamDefaults = extend({}, paramDefaults, additionalParamDefaults);
          return resourceFactory(url, extendedParamDefaults, actions, options);
        };

        return Resource;
      }

      return resourceFactory;
    }];
  });


})(window, window.angular);

/**
 * @license AngularJS v1.6.3
 * (c) 2010-2017 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

var $sanitizeMinErr = angular.$$minErr('$sanitize');
var bind;
var extend;
var forEach;
var isDefined;
var lowercase;
var noop;
var nodeContains;
var htmlParser;
var htmlSanitizeWriter;

/**
 * @ngdoc module
 * @name ngSanitize
 * @description
 *
 * # ngSanitize
 *
 * The `ngSanitize` module provides functionality to sanitize HTML.
 *
 *
 * <div doc-module-components="ngSanitize"></div>
 *
 * See {@link ngSanitize.$sanitize `$sanitize`} for usage.
 */

/**
 * @ngdoc service
 * @name $sanitize
 * @kind function
 *
 * @description
 *   Sanitizes an html string by stripping all potentially dangerous tokens.
 *
 *   The input is sanitized by parsing the HTML into tokens. All safe tokens (from a whitelist) are
 *   then serialized back to properly escaped html string. This means that no unsafe input can make
 *   it into the returned string.
 *
 *   The whitelist for URL sanitization of attribute values is configured using the functions
 *   `aHrefSanitizationWhitelist` and `imgSrcSanitizationWhitelist` of {@link ng.$compileProvider
 *   `$compileProvider`}.
 *
 *   The input may also contain SVG markup if this is enabled via {@link $sanitizeProvider}.
 *
 * @param {string} html HTML input.
 * @returns {string} Sanitized HTML.
 *
 * @example
   <example module="sanitizeExample" deps="angular-sanitize.js" name="sanitize-service">
   <file name="index.html">
     <script>
         angular.module('sanitizeExample', ['ngSanitize'])
           .controller('ExampleController', ['$scope', '$sce', function($scope, $sce) {
             $scope.snippet =
               '<p style="color:blue">an html\n' +
               '<em onmouseover="this.textContent=\'PWN3D!\'">click here</em>\n' +
               'snippet</p>';
             $scope.deliberatelyTrustDangerousSnippet = function() {
               return $sce.trustAsHtml($scope.snippet);
             };
           }]);
     </script>
     <div ng-controller="ExampleController">
        Snippet: <textarea ng-model="snippet" cols="60" rows="3"></textarea>
       <table>
         <tr>
           <td>Directive</td>
           <td>How</td>
           <td>Source</td>
           <td>Rendered</td>
         </tr>
         <tr id="bind-html-with-sanitize">
           <td>ng-bind-html</td>
           <td>Automatically uses $sanitize</td>
           <td><pre>&lt;div ng-bind-html="snippet"&gt;<br/>&lt;/div&gt;</pre></td>
           <td><div ng-bind-html="snippet"></div></td>
         </tr>
         <tr id="bind-html-with-trust">
           <td>ng-bind-html</td>
           <td>Bypass $sanitize by explicitly trusting the dangerous value</td>
           <td>
           <pre>&lt;div ng-bind-html="deliberatelyTrustDangerousSnippet()"&gt;
&lt;/div&gt;</pre>
           </td>
           <td><div ng-bind-html="deliberatelyTrustDangerousSnippet()"></div></td>
         </tr>
         <tr id="bind-default">
           <td>ng-bind</td>
           <td>Automatically escapes</td>
           <td><pre>&lt;div ng-bind="snippet"&gt;<br/>&lt;/div&gt;</pre></td>
           <td><div ng-bind="snippet"></div></td>
         </tr>
       </table>
       </div>
   </file>
   <file name="protractor.js" type="protractor">
     it('should sanitize the html snippet by default', function() {
       expect(element(by.css('#bind-html-with-sanitize div')).getAttribute('innerHTML')).
         toBe('<p>an html\n<em>click here</em>\nsnippet</p>');
     });

     it('should inline raw snippet if bound to a trusted value', function() {
       expect(element(by.css('#bind-html-with-trust div')).getAttribute('innerHTML')).
         toBe("<p style=\"color:blue\">an html\n" +
              "<em onmouseover=\"this.textContent='PWN3D!'\">click here</em>\n" +
              "snippet</p>");
     });

     it('should escape snippet without any filter', function() {
       expect(element(by.css('#bind-default div')).getAttribute('innerHTML')).
         toBe("&lt;p style=\"color:blue\"&gt;an html\n" +
              "&lt;em onmouseover=\"this.textContent='PWN3D!'\"&gt;click here&lt;/em&gt;\n" +
              "snippet&lt;/p&gt;");
     });

     it('should update', function() {
       element(by.model('snippet')).clear();
       element(by.model('snippet')).sendKeys('new <b onclick="alert(1)">text</b>');
       expect(element(by.css('#bind-html-with-sanitize div')).getAttribute('innerHTML')).
         toBe('new <b>text</b>');
       expect(element(by.css('#bind-html-with-trust div')).getAttribute('innerHTML')).toBe(
         'new <b onclick="alert(1)">text</b>');
       expect(element(by.css('#bind-default div')).getAttribute('innerHTML')).toBe(
         "new &lt;b onclick=\"alert(1)\"&gt;text&lt;/b&gt;");
     });
   </file>
   </example>
 */


/**
 * @ngdoc provider
 * @name $sanitizeProvider
 * @this
 *
 * @description
 * Creates and configures {@link $sanitize} instance.
 */
function $SanitizeProvider() {
  var svgEnabled = false;

  this.$get = ['$$sanitizeUri', function($$sanitizeUri) {
    if (svgEnabled) {
      extend(validElements, svgElements);
    }
    return function(html) {
      var buf = [];
      htmlParser(html, htmlSanitizeWriter(buf, function(uri, isImage) {
        return !/^unsafe:/.test($$sanitizeUri(uri, isImage));
      }));
      return buf.join('');
    };
  }];


  /**
   * @ngdoc method
   * @name $sanitizeProvider#enableSvg
   * @kind function
   *
   * @description
   * Enables a subset of svg to be supported by the sanitizer.
   *
   * <div class="alert alert-warning">
   *   <p>By enabling this setting without taking other precautions, you might expose your
   *   application to click-hijacking attacks. In these attacks, sanitized svg elements could be positioned
   *   outside of the containing element and be rendered over other elements on the page (e.g. a login
   *   link). Such behavior can then result in phishing incidents.</p>
   *
   *   <p>To protect against these, explicitly setup `overflow: hidden` css rule for all potential svg
   *   tags within the sanitized content:</p>
   *
   *   <br>
   *
   *   <pre><code>
   *   .rootOfTheIncludedContent svg {
   *     overflow: hidden !important;
   *   }
   *   </code></pre>
   * </div>
   *
   * @param {boolean=} flag Enable or disable SVG support in the sanitizer.
   * @returns {boolean|ng.$sanitizeProvider} Returns the currently configured value if called
   *    without an argument or self for chaining otherwise.
   */
  this.enableSvg = function(enableSvg) {
    if (isDefined(enableSvg)) {
      svgEnabled = enableSvg;
      return this;
    } else {
      return svgEnabled;
    }
  };

  //////////////////////////////////////////////////////////////////////////////////////////////////
  // Private stuff
  //////////////////////////////////////////////////////////////////////////////////////////////////

  bind = angular.bind;
  extend = angular.extend;
  forEach = angular.forEach;
  isDefined = angular.isDefined;
  lowercase = angular.lowercase;
  noop = angular.noop;

  htmlParser = htmlParserImpl;
  htmlSanitizeWriter = htmlSanitizeWriterImpl;

  nodeContains = window.Node.prototype.contains || /** @this */ function(arg) {
    // eslint-disable-next-line no-bitwise
    return !!(this.compareDocumentPosition(arg) & 16);
  };

  // Regular Expressions for parsing tags and attributes
  var SURROGATE_PAIR_REGEXP = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g,
    // Match everything outside of normal chars and " (quote character)
    NON_ALPHANUMERIC_REGEXP = /([^#-~ |!])/g;


  // Good source of info about elements and attributes
  // http://dev.w3.org/html5/spec/Overview.html#semantics
  // http://simon.html5.org/html-elements

  // Safe Void Elements - HTML5
  // http://dev.w3.org/html5/spec/Overview.html#void-elements
  var voidElements = toMap('area,br,col,hr,img,wbr');

  // Elements that you can, intentionally, leave open (and which close themselves)
  // http://dev.w3.org/html5/spec/Overview.html#optional-tags
  var optionalEndTagBlockElements = toMap('colgroup,dd,dt,li,p,tbody,td,tfoot,th,thead,tr'),
      optionalEndTagInlineElements = toMap('rp,rt'),
      optionalEndTagElements = extend({},
                                              optionalEndTagInlineElements,
                                              optionalEndTagBlockElements);

  // Safe Block Elements - HTML5
  var blockElements = extend({}, optionalEndTagBlockElements, toMap('address,article,' +
          'aside,blockquote,caption,center,del,dir,div,dl,figure,figcaption,footer,h1,h2,h3,h4,h5,' +
          'h6,header,hgroup,hr,ins,map,menu,nav,ol,pre,section,table,ul'));

  // Inline Elements - HTML5
  var inlineElements = extend({}, optionalEndTagInlineElements, toMap('a,abbr,acronym,b,' +
          'bdi,bdo,big,br,cite,code,del,dfn,em,font,i,img,ins,kbd,label,map,mark,q,ruby,rp,rt,s,' +
          'samp,small,span,strike,strong,sub,sup,time,tt,u,var'));

  // SVG Elements
  // https://wiki.whatwg.org/wiki/Sanitization_rules#svg_Elements
  // Note: the elements animate,animateColor,animateMotion,animateTransform,set are intentionally omitted.
  // They can potentially allow for arbitrary javascript to be executed. See #11290
  var svgElements = toMap('circle,defs,desc,ellipse,font-face,font-face-name,font-face-src,g,glyph,' +
          'hkern,image,linearGradient,line,marker,metadata,missing-glyph,mpath,path,polygon,polyline,' +
          'radialGradient,rect,stop,svg,switch,text,title,tspan');

  // Blocked Elements (will be stripped)
  var blockedElements = toMap('script,style');

  var validElements = extend({},
                                     voidElements,
                                     blockElements,
                                     inlineElements,
                                     optionalEndTagElements);

  //Attributes that have href and hence need to be sanitized
  var uriAttrs = toMap('background,cite,href,longdesc,src,xlink:href');

  var htmlAttrs = toMap('abbr,align,alt,axis,bgcolor,border,cellpadding,cellspacing,class,clear,' +
      'color,cols,colspan,compact,coords,dir,face,headers,height,hreflang,hspace,' +
      'ismap,lang,language,nohref,nowrap,rel,rev,rows,rowspan,rules,' +
      'scope,scrolling,shape,size,span,start,summary,tabindex,target,title,type,' +
      'valign,value,vspace,width');

  // SVG attributes (without "id" and "name" attributes)
  // https://wiki.whatwg.org/wiki/Sanitization_rules#svg_Attributes
  var svgAttrs = toMap('accent-height,accumulate,additive,alphabetic,arabic-form,ascent,' +
      'baseProfile,bbox,begin,by,calcMode,cap-height,class,color,color-rendering,content,' +
      'cx,cy,d,dx,dy,descent,display,dur,end,fill,fill-rule,font-family,font-size,font-stretch,' +
      'font-style,font-variant,font-weight,from,fx,fy,g1,g2,glyph-name,gradientUnits,hanging,' +
      'height,horiz-adv-x,horiz-origin-x,ideographic,k,keyPoints,keySplines,keyTimes,lang,' +
      'marker-end,marker-mid,marker-start,markerHeight,markerUnits,markerWidth,mathematical,' +
      'max,min,offset,opacity,orient,origin,overline-position,overline-thickness,panose-1,' +
      'path,pathLength,points,preserveAspectRatio,r,refX,refY,repeatCount,repeatDur,' +
      'requiredExtensions,requiredFeatures,restart,rotate,rx,ry,slope,stemh,stemv,stop-color,' +
      'stop-opacity,strikethrough-position,strikethrough-thickness,stroke,stroke-dasharray,' +
      'stroke-dashoffset,stroke-linecap,stroke-linejoin,stroke-miterlimit,stroke-opacity,' +
      'stroke-width,systemLanguage,target,text-anchor,to,transform,type,u1,u2,underline-position,' +
      'underline-thickness,unicode,unicode-range,units-per-em,values,version,viewBox,visibility,' +
      'width,widths,x,x-height,x1,x2,xlink:actuate,xlink:arcrole,xlink:role,xlink:show,xlink:title,' +
      'xlink:type,xml:base,xml:lang,xml:space,xmlns,xmlns:xlink,y,y1,y2,zoomAndPan', true);

  var validAttrs = extend({},
                                  uriAttrs,
                                  svgAttrs,
                                  htmlAttrs);

  function toMap(str, lowercaseKeys) {
    var obj = {}, items = str.split(','), i;
    for (i = 0; i < items.length; i++) {
      obj[lowercaseKeys ? lowercase(items[i]) : items[i]] = true;
    }
    return obj;
  }

  var inertBodyElement;
  (function(window) {
    var doc;
    if (window.document && window.document.implementation) {
      doc = window.document.implementation.createHTMLDocument('inert');
    } else {
      throw $sanitizeMinErr('noinert', 'Can\'t create an inert html document');
    }
    var docElement = doc.documentElement || doc.getDocumentElement();
    var bodyElements = docElement.getElementsByTagName('body');

    // usually there should be only one body element in the document, but IE doesn't have any, so we need to create one
    if (bodyElements.length === 1) {
      inertBodyElement = bodyElements[0];
    } else {
      var html = doc.createElement('html');
      inertBodyElement = doc.createElement('body');
      html.appendChild(inertBodyElement);
      doc.appendChild(html);
    }
  })(window);

  /**
   * @example
   * htmlParser(htmlString, {
   *     start: function(tag, attrs) {},
   *     end: function(tag) {},
   *     chars: function(text) {},
   *     comment: function(text) {}
   * });
   *
   * @param {string} html string
   * @param {object} handler
   */
  function htmlParserImpl(html, handler) {
    if (html === null || html === undefined) {
      html = '';
    } else if (typeof html !== 'string') {
      html = '' + html;
    }
    inertBodyElement.innerHTML = html;

    //mXSS protection
    var mXSSAttempts = 5;
    do {
      if (mXSSAttempts === 0) {
        throw $sanitizeMinErr('uinput', 'Failed to sanitize html because the input is unstable');
      }
      mXSSAttempts--;

      // strip custom-namespaced attributes on IE<=11
      if (window.document.documentMode) {
        stripCustomNsAttrs(inertBodyElement);
      }
      html = inertBodyElement.innerHTML; //trigger mXSS
      inertBodyElement.innerHTML = html;
    } while (html !== inertBodyElement.innerHTML);

    var node = inertBodyElement.firstChild;
    while (node) {
      switch (node.nodeType) {
        case 1: // ELEMENT_NODE
          handler.start(node.nodeName.toLowerCase(), attrToMap(node.attributes));
          break;
        case 3: // TEXT NODE
          handler.chars(node.textContent);
          break;
      }

      var nextNode;
      if (!(nextNode = node.firstChild)) {
        if (node.nodeType === 1) {
          handler.end(node.nodeName.toLowerCase());
        }
        nextNode = getNonDescendant('nextSibling', node);
        if (!nextNode) {
          while (nextNode == null) {
            node = getNonDescendant('parentNode', node);
            if (node === inertBodyElement) break;
            nextNode = getNonDescendant('nextSibling', node);
            if (node.nodeType === 1) {
              handler.end(node.nodeName.toLowerCase());
            }
          }
        }
      }
      node = nextNode;
    }

    while ((node = inertBodyElement.firstChild)) {
      inertBodyElement.removeChild(node);
    }
  }

  function attrToMap(attrs) {
    var map = {};
    for (var i = 0, ii = attrs.length; i < ii; i++) {
      var attr = attrs[i];
      map[attr.name] = attr.value;
    }
    return map;
  }


  /**
   * Escapes all potentially dangerous characters, so that the
   * resulting string can be safely inserted into attribute or
   * element text.
   * @param value
   * @returns {string} escaped text
   */
  function encodeEntities(value) {
    return value.
      replace(/&/g, '&amp;').
      replace(SURROGATE_PAIR_REGEXP, function(value) {
        var hi = value.charCodeAt(0);
        var low = value.charCodeAt(1);
        return '&#' + (((hi - 0xD800) * 0x400) + (low - 0xDC00) + 0x10000) + ';';
      }).
      replace(NON_ALPHANUMERIC_REGEXP, function(value) {
        return '&#' + value.charCodeAt(0) + ';';
      }).
      replace(/</g, '&lt;').
      replace(/>/g, '&gt;');
  }

  /**
   * create an HTML/XML writer which writes to buffer
   * @param {Array} buf use buf.join('') to get out sanitized html string
   * @returns {object} in the form of {
   *     start: function(tag, attrs) {},
   *     end: function(tag) {},
   *     chars: function(text) {},
   *     comment: function(text) {}
   * }
   */
  function htmlSanitizeWriterImpl(buf, uriValidator) {
    var ignoreCurrentElement = false;
    var out = bind(buf, buf.push);
    return {
      start: function(tag, attrs) {
        tag = lowercase(tag);
        if (!ignoreCurrentElement && blockedElements[tag]) {
          ignoreCurrentElement = tag;
        }
        if (!ignoreCurrentElement && validElements[tag] === true) {
          out('<');
          out(tag);
          forEach(attrs, function(value, key) {
            var lkey = lowercase(key);
            var isImage = (tag === 'img' && lkey === 'src') || (lkey === 'background');
            if (validAttrs[lkey] === true &&
              (uriAttrs[lkey] !== true || uriValidator(value, isImage))) {
              out(' ');
              out(key);
              out('="');
              out(encodeEntities(value));
              out('"');
            }
          });
          out('>');
        }
      },
      end: function(tag) {
        tag = lowercase(tag);
        if (!ignoreCurrentElement && validElements[tag] === true && voidElements[tag] !== true) {
          out('</');
          out(tag);
          out('>');
        }
        // eslint-disable-next-line eqeqeq
        if (tag == ignoreCurrentElement) {
          ignoreCurrentElement = false;
        }
      },
      chars: function(chars) {
        if (!ignoreCurrentElement) {
          out(encodeEntities(chars));
        }
      }
    };
  }


  /**
   * When IE9-11 comes across an unknown namespaced attribute e.g. 'xlink:foo' it adds 'xmlns:ns1' attribute to declare
   * ns1 namespace and prefixes the attribute with 'ns1' (e.g. 'ns1:xlink:foo'). This is undesirable since we don't want
   * to allow any of these custom attributes. This method strips them all.
   *
   * @param node Root element to process
   */
  function stripCustomNsAttrs(node) {
    while (node) {
      if (node.nodeType === window.Node.ELEMENT_NODE) {
        var attrs = node.attributes;
        for (var i = 0, l = attrs.length; i < l; i++) {
          var attrNode = attrs[i];
          var attrName = attrNode.name.toLowerCase();
          if (attrName === 'xmlns:ns1' || attrName.lastIndexOf('ns1:', 0) === 0) {
            node.removeAttributeNode(attrNode);
            i--;
            l--;
          }
        }
      }

      var nextNode = node.firstChild;
      if (nextNode) {
        stripCustomNsAttrs(nextNode);
      }

      node = getNonDescendant('nextSibling', node);
    }
  }

  function getNonDescendant(propName, node) {
    // An element is clobbered if its `propName` property points to one of its descendants
    var nextNode = node[propName];
    if (nextNode && nodeContains.call(node, nextNode)) {
      throw $sanitizeMinErr('elclob', 'Failed to sanitize html because the element is clobbered: {0}', node.outerHTML || node.outerText);
    }
    return nextNode;
  }
}

function sanitizeText(chars) {
  var buf = [];
  var writer = htmlSanitizeWriter(buf, noop);
  writer.chars(chars);
  return buf.join('');
}


// define ngSanitize module and register $sanitize service
angular.module('ngSanitize', [])
  .provider('$sanitize', $SanitizeProvider)
  .info({ angularVersion: '1.6.3' });

/**
 * @ngdoc filter
 * @name linky
 * @kind function
 *
 * @description
 * Finds links in text input and turns them into html links. Supports `http/https/ftp/mailto` and
 * plain email address links.
 *
 * Requires the {@link ngSanitize `ngSanitize`} module to be installed.
 *
 * @param {string} text Input text.
 * @param {string} target Window (`_blank|_self|_parent|_top`) or named frame to open links in.
 * @param {object|function(url)} [attributes] Add custom attributes to the link element.
 *
 *    Can be one of:
 *
 *    - `object`: A map of attributes
 *    - `function`: Takes the url as a parameter and returns a map of attributes
 *
 *    If the map of attributes contains a value for `target`, it overrides the value of
 *    the target parameter.
 *
 *
 * @returns {string} Html-linkified and {@link $sanitize sanitized} text.
 *
 * @usage
   <span ng-bind-html="linky_expression | linky"></span>
 *
 * @example
   <example module="linkyExample" deps="angular-sanitize.js" name="linky-filter">
     <file name="index.html">
       <div ng-controller="ExampleController">
       Snippet: <textarea ng-model="snippet" cols="60" rows="3"></textarea>
       <table>
         <tr>
           <th>Filter</th>
           <th>Source</th>
           <th>Rendered</th>
         </tr>
         <tr id="linky-filter">
           <td>linky filter</td>
           <td>
             <pre>&lt;div ng-bind-html="snippet | linky"&gt;<br>&lt;/div&gt;</pre>
           </td>
           <td>
             <div ng-bind-html="snippet | linky"></div>
           </td>
         </tr>
         <tr id="linky-target">
          <td>linky target</td>
          <td>
            <pre>&lt;div ng-bind-html="snippetWithSingleURL | linky:'_blank'"&gt;<br>&lt;/div&gt;</pre>
          </td>
          <td>
            <div ng-bind-html="snippetWithSingleURL | linky:'_blank'"></div>
          </td>
         </tr>
         <tr id="linky-custom-attributes">
          <td>linky custom attributes</td>
          <td>
            <pre>&lt;div ng-bind-html="snippetWithSingleURL | linky:'_self':{rel: 'nofollow'}"&gt;<br>&lt;/div&gt;</pre>
          </td>
          <td>
            <div ng-bind-html="snippetWithSingleURL | linky:'_self':{rel: 'nofollow'}"></div>
          </td>
         </tr>
         <tr id="escaped-html">
           <td>no filter</td>
           <td><pre>&lt;div ng-bind="snippet"&gt;<br>&lt;/div&gt;</pre></td>
           <td><div ng-bind="snippet"></div></td>
         </tr>
       </table>
     </file>
     <file name="script.js">
       angular.module('linkyExample', ['ngSanitize'])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.snippet =
             'Pretty text with some links:\n' +
             'http://angularjs.org/,\n' +
             'mailto:us@somewhere.org,\n' +
             'another@somewhere.org,\n' +
             'and one more: ftp://127.0.0.1/.';
           $scope.snippetWithSingleURL = 'http://angularjs.org/';
         }]);
     </file>
     <file name="protractor.js" type="protractor">
       it('should linkify the snippet with urls', function() {
         expect(element(by.id('linky-filter')).element(by.binding('snippet | linky')).getText()).
             toBe('Pretty text with some links: http://angularjs.org/, us@somewhere.org, ' +
                  'another@somewhere.org, and one more: ftp://127.0.0.1/.');
         expect(element.all(by.css('#linky-filter a')).count()).toEqual(4);
       });

       it('should not linkify snippet without the linky filter', function() {
         expect(element(by.id('escaped-html')).element(by.binding('snippet')).getText()).
             toBe('Pretty text with some links: http://angularjs.org/, mailto:us@somewhere.org, ' +
                  'another@somewhere.org, and one more: ftp://127.0.0.1/.');
         expect(element.all(by.css('#escaped-html a')).count()).toEqual(0);
       });

       it('should update', function() {
         element(by.model('snippet')).clear();
         element(by.model('snippet')).sendKeys('new http://link.');
         expect(element(by.id('linky-filter')).element(by.binding('snippet | linky')).getText()).
             toBe('new http://link.');
         expect(element.all(by.css('#linky-filter a')).count()).toEqual(1);
         expect(element(by.id('escaped-html')).element(by.binding('snippet')).getText())
             .toBe('new http://link.');
       });

       it('should work with the target property', function() {
        expect(element(by.id('linky-target')).
            element(by.binding("snippetWithSingleURL | linky:'_blank'")).getText()).
            toBe('http://angularjs.org/');
        expect(element(by.css('#linky-target a')).getAttribute('target')).toEqual('_blank');
       });

       it('should optionally add custom attributes', function() {
        expect(element(by.id('linky-custom-attributes')).
            element(by.binding("snippetWithSingleURL | linky:'_self':{rel: 'nofollow'}")).getText()).
            toBe('http://angularjs.org/');
        expect(element(by.css('#linky-custom-attributes a')).getAttribute('rel')).toEqual('nofollow');
       });
     </file>
   </example>
 */
angular.module('ngSanitize').filter('linky', ['$sanitize', function($sanitize) {
  var LINKY_URL_REGEXP =
        /((ftp|https?):\/\/|(www\.)|(mailto:)?[A-Za-z0-9._%+-]+@)\S*[^\s.;,(){}<>"\u201d\u2019]/i,
      MAILTO_REGEXP = /^mailto:/i;

  var linkyMinErr = angular.$$minErr('linky');
  var isDefined = angular.isDefined;
  var isFunction = angular.isFunction;
  var isObject = angular.isObject;
  var isString = angular.isString;

  return function(text, target, attributes) {
    if (text == null || text === '') return text;
    if (!isString(text)) throw linkyMinErr('notstring', 'Expected string but received: {0}', text);

    var attributesFn =
      isFunction(attributes) ? attributes :
      isObject(attributes) ? function getAttributesObject() {return attributes;} :
      function getEmptyAttributesObject() {return {};};

    var match;
    var raw = text;
    var html = [];
    var url;
    var i;
    while ((match = raw.match(LINKY_URL_REGEXP))) {
      // We can not end in these as they are sometimes found at the end of the sentence
      url = match[0];
      // if we did not match ftp/http/www/mailto then assume mailto
      if (!match[2] && !match[4]) {
        url = (match[3] ? 'http://' : 'mailto:') + url;
      }
      i = match.index;
      addText(raw.substr(0, i));
      addLink(url, match[0].replace(MAILTO_REGEXP, ''));
      raw = raw.substring(i + match[0].length);
    }
    addText(raw);
    return $sanitize(html.join(''));

    function addText(text) {
      if (!text) {
        return;
      }
      html.push(sanitizeText(text));
    }

    function addLink(url, text) {
      var key, linkAttributes = attributesFn(url);
      html.push('<a ');

      for (key in linkAttributes) {
        html.push(key + '="' + linkAttributes[key] + '" ');
      }

      if (isDefined(target) && !('target' in linkAttributes)) {
        html.push('target="',
                  target,
                  '" ');
      }
      html.push('href="',
                url.replace(/"/g, '&quot;'),
                '">');
      addText(text);
      html.push('</a>');
    }
  };
}]);


})(window, window.angular);

/*
     _ _      _       _
 ___| (_) ___| | __  (_)___
/ __| | |/ __| |/ /  | / __|
\__ \ | | (__|   < _ | \__ \
|___/_|_|\___|_|\_(_)/ |___/
                   |__/

 Version: 1.6.0
  Author: Ken Wheeler
 Website: http://kenwheeler.github.io
    Docs: http://kenwheeler.github.io/slick
    Repo: http://github.com/kenwheeler/slick
  Issues: http://github.com/kenwheeler/slick/issues

 */
/* global window, document, define, jQuery, setInterval, clearInterval */
(function(factory) {
    'use strict';
    if (typeof define === 'function' && define.amd) {
        define(['jquery'], factory);
    } else if (typeof exports !== 'undefined') {
        module.exports = factory(require('jquery'));
    } else {
        factory(jQuery);
    }

}(function($) {
    'use strict';
    var Slick = window.Slick || {};

    Slick = (function() {

        var instanceUid = 0;

        function Slick(element, settings) {

            var _ = this, dataSettings;

            _.defaults = {
                accessibility: true,
                adaptiveHeight: false,
                appendArrows: $(element),
                appendDots: $(element),
                arrows: true,
                asNavFor: null,
                prevArrow: '<button type="button" data-role="none" class="slick-prev" aria-label="Previous" tabindex="0" role="button">Previous</button>',
                nextArrow: '<button type="button" data-role="none" class="slick-next" aria-label="Next" tabindex="0" role="button">Next</button>',
                autoplay: false,
                autoplaySpeed: 3000,
                centerMode: false,
                centerPadding: '50px',
                cssEase: 'ease',
                customPaging: function(slider, i) {
                    return $('<button type="button" data-role="none" role="button" tabindex="0" />').text(i + 1);
                },
                dots: false,
                dotsClass: 'slick-dots',
                draggable: true,
                easing: 'linear',
                edgeFriction: 0.35,
                fade: false,
                focusOnSelect: false,
                infinite: true,
                initialSlide: 0,
                lazyLoad: 'ondemand',
                mobileFirst: false,
                pauseOnHover: true,
                pauseOnFocus: true,
                pauseOnDotsHover: false,
                respondTo: 'window',
                responsive: null,
                rows: 1,
                rtl: false,
                slide: '',
                slidesPerRow: 1,
                slidesToShow: 1,
                slidesToScroll: 1,
                speed: 500,
                swipe: true,
                swipeToSlide: false,
                touchMove: true,
                touchThreshold: 5,
                useCSS: true,
                useTransform: true,
                variableWidth: false,
                vertical: false,
                verticalSwiping: false,
                waitForAnimate: true,
                zIndex: 1000
            };

            _.initials = {
                animating: false,
                dragging: false,
                autoPlayTimer: null,
                currentDirection: 0,
                currentLeft: null,
                currentSlide: 0,
                direction: 1,
                $dots: null,
                listWidth: null,
                listHeight: null,
                loadIndex: 0,
                $nextArrow: null,
                $prevArrow: null,
                slideCount: null,
                slideWidth: null,
                $slideTrack: null,
                $slides: null,
                sliding: false,
                slideOffset: 0,
                swipeLeft: null,
                $list: null,
                touchObject: {},
                transformsEnabled: false,
                unslicked: false
            };

            $.extend(_, _.initials);

            _.activeBreakpoint = null;
            _.animType = null;
            _.animProp = null;
            _.breakpoints = [];
            _.breakpointSettings = [];
            _.cssTransitions = false;
            _.focussed = false;
            _.interrupted = false;
            _.hidden = 'hidden';
            _.paused = true;
            _.positionProp = null;
            _.respondTo = null;
            _.rowCount = 1;
            _.shouldClick = true;
            _.$slider = $(element);
            _.$slidesCache = null;
            _.transformType = null;
            _.transitionType = null;
            _.visibilityChange = 'visibilitychange';
            _.windowWidth = 0;
            _.windowTimer = null;

            dataSettings = $(element).data('slick') || {};

            _.options = $.extend({}, _.defaults, settings, dataSettings);

            _.currentSlide = _.options.initialSlide;

            _.originalSettings = _.options;

            if (typeof document.mozHidden !== 'undefined') {
                _.hidden = 'mozHidden';
                _.visibilityChange = 'mozvisibilitychange';
            } else if (typeof document.webkitHidden !== 'undefined') {
                _.hidden = 'webkitHidden';
                _.visibilityChange = 'webkitvisibilitychange';
            }

            _.autoPlay = $.proxy(_.autoPlay, _);
            _.autoPlayClear = $.proxy(_.autoPlayClear, _);
            _.autoPlayIterator = $.proxy(_.autoPlayIterator, _);
            _.changeSlide = $.proxy(_.changeSlide, _);
            _.clickHandler = $.proxy(_.clickHandler, _);
            _.selectHandler = $.proxy(_.selectHandler, _);
            _.setPosition = $.proxy(_.setPosition, _);
            _.swipeHandler = $.proxy(_.swipeHandler, _);
            _.dragHandler = $.proxy(_.dragHandler, _);
            _.keyHandler = $.proxy(_.keyHandler, _);

            _.instanceUid = instanceUid++;

            // A simple way to check for HTML strings
            // Strict HTML recognition (must start with <)
            // Extracted from jQuery v1.11 source
            _.htmlExpr = /^(?:\s*(<[\w\W]+>)[^>]*)$/;


            _.registerBreakpoints();
            _.init(true);

        }

        return Slick;

    }());

    Slick.prototype.activateADA = function() {
        var _ = this;

        _.$slideTrack.find('.slick-active').attr({
            'aria-hidden': 'false'
        }).find('a, input, button, select').attr({
            'tabindex': '0'
        });

    };

    Slick.prototype.addSlide = Slick.prototype.slickAdd = function(markup, index, addBefore) {

        var _ = this;

        if (typeof(index) === 'boolean') {
            addBefore = index;
            index = null;
        } else if (index < 0 || (index >= _.slideCount)) {
            return false;
        }

        _.unload();

        if (typeof(index) === 'number') {
            if (index === 0 && _.$slides.length === 0) {
                $(markup).appendTo(_.$slideTrack);
            } else if (addBefore) {
                $(markup).insertBefore(_.$slides.eq(index));
            } else {
                $(markup).insertAfter(_.$slides.eq(index));
            }
        } else {
            if (addBefore === true) {
                $(markup).prependTo(_.$slideTrack);
            } else {
                $(markup).appendTo(_.$slideTrack);
            }
        }

        _.$slides = _.$slideTrack.children(this.options.slide);

        _.$slideTrack.children(this.options.slide).detach();

        _.$slideTrack.append(_.$slides);

        _.$slides.each(function(index, element) {
            $(element).attr('data-slick-index', index);
        });

        _.$slidesCache = _.$slides;

        _.reinit();

    };

    Slick.prototype.animateHeight = function() {
        var _ = this;
        if (_.options.slidesToShow === 1 && _.options.adaptiveHeight === true && _.options.vertical === false) {
            var targetHeight = _.$slides.eq(_.currentSlide).outerHeight(true);
            _.$list.animate({
                height: targetHeight
            }, _.options.speed);
        }
    };

    Slick.prototype.animateSlide = function(targetLeft, callback) {

        var animProps = {},
            _ = this;

        _.animateHeight();

        if (_.options.rtl === true && _.options.vertical === false) {
            targetLeft = -targetLeft;
        }
        if (_.transformsEnabled === false) {
            if (_.options.vertical === false) {
                _.$slideTrack.animate({
                    left: targetLeft
                }, _.options.speed, _.options.easing, callback);
            } else {
                _.$slideTrack.animate({
                    top: targetLeft
                }, _.options.speed, _.options.easing, callback);
            }

        } else {

            if (_.cssTransitions === false) {
                if (_.options.rtl === true) {
                    _.currentLeft = -(_.currentLeft);
                }
                $({
                    animStart: _.currentLeft
                }).animate({
                    animStart: targetLeft
                }, {
                    duration: _.options.speed,
                    easing: _.options.easing,
                    step: function(now) {
                        now = Math.ceil(now);
                        if (_.options.vertical === false) {
                            animProps[_.animType] = 'translate(' +
                                now + 'px, 0px)';
                            _.$slideTrack.css(animProps);
                        } else {
                            animProps[_.animType] = 'translate(0px,' +
                                now + 'px)';
                            _.$slideTrack.css(animProps);
                        }
                    },
                    complete: function() {
                        if (callback) {
                            callback.call();
                        }
                    }
                });

            } else {

                _.applyTransition();
                targetLeft = Math.ceil(targetLeft);

                if (_.options.vertical === false) {
                    animProps[_.animType] = 'translate3d(' + targetLeft + 'px, 0px, 0px)';
                } else {
                    animProps[_.animType] = 'translate3d(0px,' + targetLeft + 'px, 0px)';
                }
                _.$slideTrack.css(animProps);

                if (callback) {
                    setTimeout(function() {

                        _.disableTransition();

                        callback.call();
                    }, _.options.speed);
                }

            }

        }

    };

    Slick.prototype.getNavTarget = function() {

        var _ = this,
            asNavFor = _.options.asNavFor;

        if ( asNavFor && asNavFor !== null ) {
            asNavFor = $(asNavFor).not(_.$slider);
        }

        return asNavFor;

    };

    Slick.prototype.asNavFor = function(index) {

        var _ = this,
            asNavFor = _.getNavTarget();

        if ( asNavFor !== null && typeof asNavFor === 'object' ) {
            asNavFor.each(function() {
                var target = $(this).slick('getSlick');
                if(!target.unslicked) {
                    target.slideHandler(index, true);
                }
            });
        }

    };

    Slick.prototype.applyTransition = function(slide) {

        var _ = this,
            transition = {};

        if (_.options.fade === false) {
            transition[_.transitionType] = _.transformType + ' ' + _.options.speed + 'ms ' + _.options.cssEase;
        } else {
            transition[_.transitionType] = 'opacity ' + _.options.speed + 'ms ' + _.options.cssEase;
        }

        if (_.options.fade === false) {
            _.$slideTrack.css(transition);
        } else {
            _.$slides.eq(slide).css(transition);
        }

    };

    Slick.prototype.autoPlay = function() {

        var _ = this;

        _.autoPlayClear();

        if ( _.slideCount > _.options.slidesToShow ) {
            _.autoPlayTimer = setInterval( _.autoPlayIterator, _.options.autoplaySpeed );
        }

    };

    Slick.prototype.autoPlayClear = function() {

        var _ = this;

        if (_.autoPlayTimer) {
            clearInterval(_.autoPlayTimer);
        }

    };

    Slick.prototype.autoPlayIterator = function() {

        var _ = this,
            slideTo = _.currentSlide + _.options.slidesToScroll;

        if ( !_.paused && !_.interrupted && !_.focussed ) {

            if ( _.options.infinite === false ) {

                if ( _.direction === 1 && ( _.currentSlide + 1 ) === ( _.slideCount - 1 )) {
                    _.direction = 0;
                }

                else if ( _.direction === 0 ) {

                    slideTo = _.currentSlide - _.options.slidesToScroll;

                    if ( _.currentSlide - 1 === 0 ) {
                        _.direction = 1;
                    }

                }

            }

            _.slideHandler( slideTo );

        }

    };

    Slick.prototype.buildArrows = function() {

        var _ = this;

        if (_.options.arrows === true ) {

            _.$prevArrow = $(_.options.prevArrow).addClass('slick-arrow');
            _.$nextArrow = $(_.options.nextArrow).addClass('slick-arrow');

            if( _.slideCount > _.options.slidesToShow ) {

                _.$prevArrow.removeClass('slick-hidden').removeAttr('aria-hidden tabindex');
                _.$nextArrow.removeClass('slick-hidden').removeAttr('aria-hidden tabindex');

                if (_.htmlExpr.test(_.options.prevArrow)) {
                    _.$prevArrow.prependTo(_.options.appendArrows);
                }

                if (_.htmlExpr.test(_.options.nextArrow)) {
                    _.$nextArrow.appendTo(_.options.appendArrows);
                }

                if (_.options.infinite !== true) {
                    _.$prevArrow
                        .addClass('slick-disabled')
                        .attr('aria-disabled', 'true');
                }

            } else {

                _.$prevArrow.add( _.$nextArrow )

                    .addClass('slick-hidden')
                    .attr({
                        'aria-disabled': 'true',
                        'tabindex': '-1'
                    });

            }

        }

    };

    Slick.prototype.buildDots = function() {

        var _ = this,
            i, dot;

        if (_.options.dots === true && _.slideCount > _.options.slidesToShow) {

            _.$slider.addClass('slick-dotted');

            dot = $('<ul />').addClass(_.options.dotsClass);

            for (i = 0; i <= _.getDotCount(); i += 1) {
                dot.append($('<li />').append(_.options.customPaging.call(this, _, i)));
            }

            _.$dots = dot.appendTo(_.options.appendDots);

            _.$dots.find('li').first().addClass('slick-active').attr('aria-hidden', 'false');

        }

    };

    Slick.prototype.buildOut = function() {

        var _ = this;

        _.$slides =
            _.$slider
                .children( _.options.slide + ':not(.slick-cloned)')
                .addClass('slick-slide');

        _.slideCount = _.$slides.length;

        _.$slides.each(function(index, element) {
            $(element)
                .attr('data-slick-index', index)
                .data('originalStyling', $(element).attr('style') || '');
        });

        _.$slider.addClass('slick-slider');

        _.$slideTrack = (_.slideCount === 0) ?
            $('<div class="slick-track"/>').appendTo(_.$slider) :
            _.$slides.wrapAll('<div class="slick-track"/>').parent();

        _.$list = _.$slideTrack.wrap(
            '<div aria-live="polite" class="slick-list"/>').parent();
        _.$slideTrack.css('opacity', 0);

        if (_.options.centerMode === true || _.options.swipeToSlide === true) {
            _.options.slidesToScroll = 1;
        }

        $('img[data-lazy]', _.$slider).not('[src]').addClass('slick-loading');

        _.setupInfinite();

        _.buildArrows();

        _.buildDots();

        _.updateDots();


        _.setSlideClasses(typeof _.currentSlide === 'number' ? _.currentSlide : 0);

        if (_.options.draggable === true) {
            _.$list.addClass('draggable');
        }

    };

    Slick.prototype.buildRows = function() {

        var _ = this, a, b, c, newSlides, numOfSlides, originalSlides,slidesPerSection;

        newSlides = document.createDocumentFragment();
        originalSlides = _.$slider.children();

        if(_.options.rows > 1) {

            slidesPerSection = _.options.slidesPerRow * _.options.rows;
            numOfSlides = Math.ceil(
                originalSlides.length / slidesPerSection
            );

            for(a = 0; a < numOfSlides; a++){
                var slide = document.createElement('div');
                for(b = 0; b < _.options.rows; b++) {
                    var row = document.createElement('div');
                    for(c = 0; c < _.options.slidesPerRow; c++) {
                        var target = (a * slidesPerSection + ((b * _.options.slidesPerRow) + c));
                        if (originalSlides.get(target)) {
                            row.appendChild(originalSlides.get(target));
                        }
                    }
                    slide.appendChild(row);
                }
                newSlides.appendChild(slide);
            }

            _.$slider.empty().append(newSlides);
            _.$slider.children().children().children()
                .css({
                    'width':(100 / _.options.slidesPerRow) + '%',
                    'display': 'inline-block'
                });

        }

    };

    Slick.prototype.checkResponsive = function(initial, forceUpdate) {

        var _ = this,
            breakpoint, targetBreakpoint, respondToWidth, triggerBreakpoint = false;
        var sliderWidth = _.$slider.width();
        var windowWidth = window.innerWidth || $(window).width();

        if (_.respondTo === 'window') {
            respondToWidth = windowWidth;
        } else if (_.respondTo === 'slider') {
            respondToWidth = sliderWidth;
        } else if (_.respondTo === 'min') {
            respondToWidth = Math.min(windowWidth, sliderWidth);
        }

        if ( _.options.responsive &&
            _.options.responsive.length &&
            _.options.responsive !== null) {

            targetBreakpoint = null;

            for (breakpoint in _.breakpoints) {
                if (_.breakpoints.hasOwnProperty(breakpoint)) {
                    if (_.originalSettings.mobileFirst === false) {
                        if (respondToWidth < _.breakpoints[breakpoint]) {
                            targetBreakpoint = _.breakpoints[breakpoint];
                        }
                    } else {
                        if (respondToWidth > _.breakpoints[breakpoint]) {
                            targetBreakpoint = _.breakpoints[breakpoint];
                        }
                    }
                }
            }

            if (targetBreakpoint !== null) {
                if (_.activeBreakpoint !== null) {
                    if (targetBreakpoint !== _.activeBreakpoint || forceUpdate) {
                        _.activeBreakpoint =
                            targetBreakpoint;
                        if (_.breakpointSettings[targetBreakpoint] === 'unslick') {
                            _.unslick(targetBreakpoint);
                        } else {
                            _.options = $.extend({}, _.originalSettings,
                                _.breakpointSettings[
                                    targetBreakpoint]);
                            if (initial === true) {
                                _.currentSlide = _.options.initialSlide;
                            }
                            _.refresh(initial);
                        }
                        triggerBreakpoint = targetBreakpoint;
                    }
                } else {
                    _.activeBreakpoint = targetBreakpoint;
                    if (_.breakpointSettings[targetBreakpoint] === 'unslick') {
                        _.unslick(targetBreakpoint);
                    } else {
                        _.options = $.extend({}, _.originalSettings,
                            _.breakpointSettings[
                                targetBreakpoint]);
                        if (initial === true) {
                            _.currentSlide = _.options.initialSlide;
                        }
                        _.refresh(initial);
                    }
                    triggerBreakpoint = targetBreakpoint;
                }
            } else {
                if (_.activeBreakpoint !== null) {
                    _.activeBreakpoint = null;
                    _.options = _.originalSettings;
                    if (initial === true) {
                        _.currentSlide = _.options.initialSlide;
                    }
                    _.refresh(initial);
                    triggerBreakpoint = targetBreakpoint;
                }
            }

            // only trigger breakpoints during an actual break. not on initialize.
            if( !initial && triggerBreakpoint !== false ) {
                _.$slider.trigger('breakpoint', [_, triggerBreakpoint]);
            }
        }

    };

    Slick.prototype.changeSlide = function(event, dontAnimate) {

        var _ = this,
            $target = $(event.currentTarget),
            indexOffset, slideOffset, unevenOffset;

        // If target is a link, prevent default action.
        if($target.is('a')) {
            event.preventDefault();
        }

        // If target is not the <li> element (ie: a child), find the <li>.
        if(!$target.is('li')) {
            $target = $target.closest('li');
        }

        unevenOffset = (_.slideCount % _.options.slidesToScroll !== 0);
        indexOffset = unevenOffset ? 0 : (_.slideCount - _.currentSlide) % _.options.slidesToScroll;

        switch (event.data.message) {

            case 'previous':
                slideOffset = indexOffset === 0 ? _.options.slidesToScroll : _.options.slidesToShow - indexOffset;
                if (_.slideCount > _.options.slidesToShow) {
                    _.slideHandler(_.currentSlide - slideOffset, false, dontAnimate);
                }
                break;

            case 'next':
                slideOffset = indexOffset === 0 ? _.options.slidesToScroll : indexOffset;
                if (_.slideCount > _.options.slidesToShow) {
                    _.slideHandler(_.currentSlide + slideOffset, false, dontAnimate);
                }
                break;

            case 'index':
                var index = event.data.index === 0 ? 0 :
                    event.data.index || $target.index() * _.options.slidesToScroll;

                _.slideHandler(_.checkNavigable(index), false, dontAnimate);
                $target.children().trigger('focus');
                break;

            default:
                return;
        }

    };

    Slick.prototype.checkNavigable = function(index) {

        var _ = this,
            navigables, prevNavigable;

        navigables = _.getNavigableIndexes();
        prevNavigable = 0;
        if (index > navigables[navigables.length - 1]) {
            index = navigables[navigables.length - 1];
        } else {
            for (var n in navigables) {
                if (index < navigables[n]) {
                    index = prevNavigable;
                    break;
                }
                prevNavigable = navigables[n];
            }
        }

        return index;
    };

    Slick.prototype.cleanUpEvents = function() {

        var _ = this;

        if (_.options.dots && _.$dots !== null) {

            $('li', _.$dots)
                .off('click.slick', _.changeSlide)
                .off('mouseenter.slick', $.proxy(_.interrupt, _, true))
                .off('mouseleave.slick', $.proxy(_.interrupt, _, false));

        }

        _.$slider.off('focus.slick blur.slick');

        if (_.options.arrows === true && _.slideCount > _.options.slidesToShow) {
            _.$prevArrow && _.$prevArrow.off('click.slick', _.changeSlide);
            _.$nextArrow && _.$nextArrow.off('click.slick', _.changeSlide);
        }

        _.$list.off('touchstart.slick mousedown.slick', _.swipeHandler);
        _.$list.off('touchmove.slick mousemove.slick', _.swipeHandler);
        _.$list.off('touchend.slick mouseup.slick', _.swipeHandler);
        _.$list.off('touchcancel.slick mouseleave.slick', _.swipeHandler);

        _.$list.off('click.slick', _.clickHandler);

        $(document).off(_.visibilityChange, _.visibility);

        _.cleanUpSlideEvents();

        if (_.options.accessibility === true) {
            _.$list.off('keydown.slick', _.keyHandler);
        }

        if (_.options.focusOnSelect === true) {
            $(_.$slideTrack).children().off('click.slick', _.selectHandler);
        }

        $(window).off('orientationchange.slick.slick-' + _.instanceUid, _.orientationChange);

        $(window).off('resize.slick.slick-' + _.instanceUid, _.resize);

        $('[draggable!=true]', _.$slideTrack).off('dragstart', _.preventDefault);

        $(window).off('load.slick.slick-' + _.instanceUid, _.setPosition);
        $(document).off('ready.slick.slick-' + _.instanceUid, _.setPosition);

    };

    Slick.prototype.cleanUpSlideEvents = function() {

        var _ = this;

        _.$list.off('mouseenter.slick', $.proxy(_.interrupt, _, true));
        _.$list.off('mouseleave.slick', $.proxy(_.interrupt, _, false));

    };

    Slick.prototype.cleanUpRows = function() {

        var _ = this, originalSlides;

        if(_.options.rows > 1) {
            originalSlides = _.$slides.children().children();
            originalSlides.removeAttr('style');
            _.$slider.empty().append(originalSlides);
        }

    };

    Slick.prototype.clickHandler = function(event) {

        var _ = this;

        if (_.shouldClick === false) {
            event.stopImmediatePropagation();
            event.stopPropagation();
            event.preventDefault();
        }

    };

    Slick.prototype.destroy = function(refresh) {

        var _ = this;

        _.autoPlayClear();

        _.touchObject = {};

        _.cleanUpEvents();

        $('.slick-cloned', _.$slider).detach();

        if (_.$dots) {
            _.$dots.remove();
        }


        if ( _.$prevArrow && _.$prevArrow.length ) {

            _.$prevArrow
                .removeClass('slick-disabled slick-arrow slick-hidden')
                .removeAttr('aria-hidden aria-disabled tabindex')
                .css('display','');

            if ( _.htmlExpr.test( _.options.prevArrow )) {
                _.$prevArrow.remove();
            }
        }

        if ( _.$nextArrow && _.$nextArrow.length ) {

            _.$nextArrow
                .removeClass('slick-disabled slick-arrow slick-hidden')
                .removeAttr('aria-hidden aria-disabled tabindex')
                .css('display','');

            if ( _.htmlExpr.test( _.options.nextArrow )) {
                _.$nextArrow.remove();
            }

        }


        if (_.$slides) {

            _.$slides
                .removeClass('slick-slide slick-active slick-center slick-visible slick-current')
                .removeAttr('aria-hidden')
                .removeAttr('data-slick-index')
                .each(function(){
                    $(this).attr('style', $(this).data('originalStyling'));
                });

            _.$slideTrack.children(this.options.slide).detach();

            _.$slideTrack.detach();

            _.$list.detach();

            _.$slider.append(_.$slides);
        }

        _.cleanUpRows();

        _.$slider.removeClass('slick-slider');
        _.$slider.removeClass('slick-initialized');
        _.$slider.removeClass('slick-dotted');

        _.unslicked = true;

        if(!refresh) {
            _.$slider.trigger('destroy', [_]);
        }

    };

    Slick.prototype.disableTransition = function(slide) {

        var _ = this,
            transition = {};

        transition[_.transitionType] = '';

        if (_.options.fade === false) {
            _.$slideTrack.css(transition);
        } else {
            _.$slides.eq(slide).css(transition);
        }

    };

    Slick.prototype.fadeSlide = function(slideIndex, callback) {

        var _ = this;

        if (_.cssTransitions === false) {

            _.$slides.eq(slideIndex).css({
                zIndex: _.options.zIndex
            });

            _.$slides.eq(slideIndex).animate({
                opacity: 1
            }, _.options.speed, _.options.easing, callback);

        } else {

            _.applyTransition(slideIndex);

            _.$slides.eq(slideIndex).css({
                opacity: 1,
                zIndex: _.options.zIndex
            });

            if (callback) {
                setTimeout(function() {

                    _.disableTransition(slideIndex);

                    callback.call();
                }, _.options.speed);
            }

        }

    };

    Slick.prototype.fadeSlideOut = function(slideIndex) {

        var _ = this;

        if (_.cssTransitions === false) {

            _.$slides.eq(slideIndex).animate({
                opacity: 0,
                zIndex: _.options.zIndex - 2
            }, _.options.speed, _.options.easing);

        } else {

            _.applyTransition(slideIndex);

            _.$slides.eq(slideIndex).css({
                opacity: 0,
                zIndex: _.options.zIndex - 2
            });

        }

    };

    Slick.prototype.filterSlides = Slick.prototype.slickFilter = function(filter) {

        var _ = this;

        if (filter !== null) {

            _.$slidesCache = _.$slides;

            _.unload();

            _.$slideTrack.children(this.options.slide).detach();

            _.$slidesCache.filter(filter).appendTo(_.$slideTrack);

            _.reinit();

        }

    };

    Slick.prototype.focusHandler = function() {

        var _ = this;

        _.$slider
            .off('focus.slick blur.slick')
            .on('focus.slick blur.slick',
                '*:not(.slick-arrow)', function(event) {

            event.stopImmediatePropagation();
            var $sf = $(this);

            setTimeout(function() {

                if( _.options.pauseOnFocus ) {
                    _.focussed = $sf.is(':focus');
                    _.autoPlay();
                }

            }, 0);

        });
    };

    Slick.prototype.getCurrent = Slick.prototype.slickCurrentSlide = function() {

        var _ = this;
        return _.currentSlide;

    };

    Slick.prototype.getDotCount = function() {

        var _ = this;

        var breakPoint = 0;
        var counter = 0;
        var pagerQty = 0;

        if (_.options.infinite === true) {
            while (breakPoint < _.slideCount) {
                ++pagerQty;
                breakPoint = counter + _.options.slidesToScroll;
                counter += _.options.slidesToScroll <= _.options.slidesToShow ? _.options.slidesToScroll : _.options.slidesToShow;
            }
        } else if (_.options.centerMode === true) {
            pagerQty = _.slideCount;
        } else if(!_.options.asNavFor) {
            pagerQty = 1 + Math.ceil((_.slideCount - _.options.slidesToShow) / _.options.slidesToScroll);
        }else {
            while (breakPoint < _.slideCount) {
                ++pagerQty;
                breakPoint = counter + _.options.slidesToScroll;
                counter += _.options.slidesToScroll <= _.options.slidesToShow ? _.options.slidesToScroll : _.options.slidesToShow;
            }
        }

        return pagerQty - 1;

    };

    Slick.prototype.getLeft = function(slideIndex) {

        var _ = this,
            targetLeft,
            verticalHeight,
            verticalOffset = 0,
            targetSlide;

        _.slideOffset = 0;
        verticalHeight = _.$slides.first().outerHeight(true);

        if (_.options.infinite === true) {
            if (_.slideCount > _.options.slidesToShow) {
                _.slideOffset = (_.slideWidth * _.options.slidesToShow) * -1;
                verticalOffset = (verticalHeight * _.options.slidesToShow) * -1;
            }
            if (_.slideCount % _.options.slidesToScroll !== 0) {
                if (slideIndex + _.options.slidesToScroll > _.slideCount && _.slideCount > _.options.slidesToShow) {
                    if (slideIndex > _.slideCount) {
                        _.slideOffset = ((_.options.slidesToShow - (slideIndex - _.slideCount)) * _.slideWidth) * -1;
                        verticalOffset = ((_.options.slidesToShow - (slideIndex - _.slideCount)) * verticalHeight) * -1;
                    } else {
                        _.slideOffset = ((_.slideCount % _.options.slidesToScroll) * _.slideWidth) * -1;
                        verticalOffset = ((_.slideCount % _.options.slidesToScroll) * verticalHeight) * -1;
                    }
                }
            }
        } else {
            if (slideIndex + _.options.slidesToShow > _.slideCount) {
                _.slideOffset = ((slideIndex + _.options.slidesToShow) - _.slideCount) * _.slideWidth;
                verticalOffset = ((slideIndex + _.options.slidesToShow) - _.slideCount) * verticalHeight;
            }
        }

        if (_.slideCount <= _.options.slidesToShow) {
            _.slideOffset = 0;
            verticalOffset = 0;
        }

        if (_.options.centerMode === true && _.options.infinite === true) {
            _.slideOffset += _.slideWidth * Math.floor(_.options.slidesToShow / 2) - _.slideWidth;
        } else if (_.options.centerMode === true) {
            _.slideOffset = 0;
            _.slideOffset += _.slideWidth * Math.floor(_.options.slidesToShow / 2);
        }

        if (_.options.vertical === false) {
            targetLeft = ((slideIndex * _.slideWidth) * -1) + _.slideOffset;
        } else {
            targetLeft = ((slideIndex * verticalHeight) * -1) + verticalOffset;
        }

        if (_.options.variableWidth === true) {

            if (_.slideCount <= _.options.slidesToShow || _.options.infinite === false) {
                targetSlide = _.$slideTrack.children('.slick-slide').eq(slideIndex);
            } else {
                targetSlide = _.$slideTrack.children('.slick-slide').eq(slideIndex + _.options.slidesToShow);
            }

            if (_.options.rtl === true) {
                if (targetSlide[0]) {
                    targetLeft = (_.$slideTrack.width() - targetSlide[0].offsetLeft - targetSlide.width()) * -1;
                } else {
                    targetLeft =  0;
                }
            } else {
                targetLeft = targetSlide[0] ? targetSlide[0].offsetLeft * -1 : 0;
            }

            if (_.options.centerMode === true) {
                if (_.slideCount <= _.options.slidesToShow || _.options.infinite === false) {
                    targetSlide = _.$slideTrack.children('.slick-slide').eq(slideIndex);
                } else {
                    targetSlide = _.$slideTrack.children('.slick-slide').eq(slideIndex + _.options.slidesToShow + 1);
                }

                if (_.options.rtl === true) {
                    if (targetSlide[0]) {
                        targetLeft = (_.$slideTrack.width() - targetSlide[0].offsetLeft - targetSlide.width()) * -1;
                    } else {
                        targetLeft =  0;
                    }
                } else {
                    targetLeft = targetSlide[0] ? targetSlide[0].offsetLeft * -1 : 0;
                }

                targetLeft += (_.$list.width() - targetSlide.outerWidth()) / 2;
            }
        }

        return targetLeft;

    };

    Slick.prototype.getOption = Slick.prototype.slickGetOption = function(option) {

        var _ = this;

        return _.options[option];

    };

    Slick.prototype.getNavigableIndexes = function() {

        var _ = this,
            breakPoint = 0,
            counter = 0,
            indexes = [],
            max;

        if (_.options.infinite === false) {
            max = _.slideCount;
        } else {
            breakPoint = _.options.slidesToScroll * -1;
            counter = _.options.slidesToScroll * -1;
            max = _.slideCount * 2;
        }

        while (breakPoint < max) {
            indexes.push(breakPoint);
            breakPoint = counter + _.options.slidesToScroll;
            counter += _.options.slidesToScroll <= _.options.slidesToShow ? _.options.slidesToScroll : _.options.slidesToShow;
        }

        return indexes;

    };

    Slick.prototype.getSlick = function() {

        return this;

    };

    Slick.prototype.getSlideCount = function() {

        var _ = this,
            slidesTraversed, swipedSlide, centerOffset;

        centerOffset = _.options.centerMode === true ? _.slideWidth * Math.floor(_.options.slidesToShow / 2) : 0;

        if (_.options.swipeToSlide === true) {
            _.$slideTrack.find('.slick-slide').each(function(index, slide) {
                if (slide.offsetLeft - centerOffset + ($(slide).outerWidth() / 2) > (_.swipeLeft * -1)) {
                    swipedSlide = slide;
                    return false;
                }
            });

            slidesTraversed = Math.abs($(swipedSlide).attr('data-slick-index') - _.currentSlide) || 1;

            return slidesTraversed;

        } else {
            return _.options.slidesToScroll;
        }

    };

    Slick.prototype.goTo = Slick.prototype.slickGoTo = function(slide, dontAnimate) {

        var _ = this;

        _.changeSlide({
            data: {
                message: 'index',
                index: parseInt(slide)
            }
        }, dontAnimate);

    };

    Slick.prototype.init = function(creation) {

        var _ = this;

        if (!$(_.$slider).hasClass('slick-initialized')) {

            $(_.$slider).addClass('slick-initialized');

            _.buildRows();
            _.buildOut();
            _.setProps();
            _.startLoad();
            _.loadSlider();
            _.initializeEvents();
            _.updateArrows();
            _.updateDots();
            _.checkResponsive(true);
            _.focusHandler();

        }

        if (creation) {
            _.$slider.trigger('init', [_]);
        }

        if (_.options.accessibility === true) {
            _.initADA();
        }

        if ( _.options.autoplay ) {

            _.paused = false;
            _.autoPlay();

        }

    };

    Slick.prototype.initADA = function() {
        var _ = this;
        _.$slides.add(_.$slideTrack.find('.slick-cloned')).attr({
            'aria-hidden': 'true',
            'tabindex': '-1'
        }).find('a, input, button, select').attr({
            'tabindex': '-1'
        });

        _.$slideTrack.attr('role', 'listbox');

        _.$slides.not(_.$slideTrack.find('.slick-cloned')).each(function(i) {
            $(this).attr({
                'role': 'option',
                'aria-describedby': 'slick-slide' + _.instanceUid + i + ''
            });
        });

        if (_.$dots !== null) {
            _.$dots.attr('role', 'tablist').find('li').each(function(i) {
                $(this).attr({
                    'role': 'presentation',
                    'aria-selected': 'false',
                    'aria-controls': 'navigation' + _.instanceUid + i + '',
                    'id': 'slick-slide' + _.instanceUid + i + ''
                });
            })
                .first().attr('aria-selected', 'true').end()
                .find('button').attr('role', 'button').end()
                .closest('div').attr('role', 'toolbar');
        }
        _.activateADA();

    };

    Slick.prototype.initArrowEvents = function() {

        var _ = this;

        if (_.options.arrows === true && _.slideCount > _.options.slidesToShow) {
            _.$prevArrow
               .off('click.slick')
               .on('click.slick', {
                    message: 'previous'
               }, _.changeSlide);
            _.$nextArrow
               .off('click.slick')
               .on('click.slick', {
                    message: 'next'
               }, _.changeSlide);
        }

    };

    Slick.prototype.initDotEvents = function() {

        var _ = this;

        if (_.options.dots === true && _.slideCount > _.options.slidesToShow) {
            $('li', _.$dots).on('click.slick', {
                message: 'index'
            }, _.changeSlide);
        }

        if ( _.options.dots === true && _.options.pauseOnDotsHover === true ) {

            $('li', _.$dots)
                .on('mouseenter.slick', $.proxy(_.interrupt, _, true))
                .on('mouseleave.slick', $.proxy(_.interrupt, _, false));

        }

    };

    Slick.prototype.initSlideEvents = function() {

        var _ = this;

        if ( _.options.pauseOnHover ) {

            _.$list.on('mouseenter.slick', $.proxy(_.interrupt, _, true));
            _.$list.on('mouseleave.slick', $.proxy(_.interrupt, _, false));

        }

    };

    Slick.prototype.initializeEvents = function() {

        var _ = this;

        _.initArrowEvents();

        _.initDotEvents();
        _.initSlideEvents();

        _.$list.on('touchstart.slick mousedown.slick', {
            action: 'start'
        }, _.swipeHandler);
        _.$list.on('touchmove.slick mousemove.slick', {
            action: 'move'
        }, _.swipeHandler);
        _.$list.on('touchend.slick mouseup.slick', {
            action: 'end'
        }, _.swipeHandler);
        _.$list.on('touchcancel.slick mouseleave.slick', {
            action: 'end'
        }, _.swipeHandler);

        _.$list.on('click.slick', _.clickHandler);

        $(document).on(_.visibilityChange, $.proxy(_.visibility, _));

        if (_.options.accessibility === true) {
            _.$list.on('keydown.slick', _.keyHandler);
        }

        if (_.options.focusOnSelect === true) {
            $(_.$slideTrack).children().on('click.slick', _.selectHandler);
        }

        $(window).on('orientationchange.slick.slick-' + _.instanceUid, $.proxy(_.orientationChange, _));

        $(window).on('resize.slick.slick-' + _.instanceUid, $.proxy(_.resize, _));

        $('[draggable!=true]', _.$slideTrack).on('dragstart', _.preventDefault);

        $(window).on('load.slick.slick-' + _.instanceUid, _.setPosition);
        $(document).on('ready.slick.slick-' + _.instanceUid, _.setPosition);

    };

    Slick.prototype.initUI = function() {

        var _ = this;

        if (_.options.arrows === true && _.slideCount > _.options.slidesToShow) {

            _.$prevArrow.show();
            _.$nextArrow.show();

        }

        if (_.options.dots === true && _.slideCount > _.options.slidesToShow) {

            _.$dots.show();

        }

    };

    Slick.prototype.keyHandler = function(event) {

        var _ = this;
         //Dont slide if the cursor is inside the form fields and arrow keys are pressed
        if(!event.target.tagName.match('TEXTAREA|INPUT|SELECT')) {
            if (event.keyCode === 37 && _.options.accessibility === true) {
                _.changeSlide({
                    data: {
                        message: _.options.rtl === true ? 'next' :  'previous'
                    }
                });
            } else if (event.keyCode === 39 && _.options.accessibility === true) {
                _.changeSlide({
                    data: {
                        message: _.options.rtl === true ? 'previous' : 'next'
                    }
                });
            }
        }

    };

    Slick.prototype.lazyLoad = function() {

        var _ = this,
            loadRange, cloneRange, rangeStart, rangeEnd;

        function loadImages(imagesScope) {

            $('img[data-lazy]', imagesScope).each(function() {

                var image = $(this),
                    imageSource = $(this).attr('data-lazy'),
                    imageToLoad = document.createElement('img');

                imageToLoad.onload = function() {

                    image
                        .animate({ opacity: 0 }, 100, function() {
                            image
                                .attr('src', imageSource)
                                .animate({ opacity: 1 }, 200, function() {
                                    image
                                        .removeAttr('data-lazy')
                                        .removeClass('slick-loading');
                                });
                            _.$slider.trigger('lazyLoaded', [_, image, imageSource]);
                        });

                };

                imageToLoad.onerror = function() {

                    image
                        .removeAttr( 'data-lazy' )
                        .removeClass( 'slick-loading' )
                        .addClass( 'slick-lazyload-error' );

                    _.$slider.trigger('lazyLoadError', [ _, image, imageSource ]);

                };

                imageToLoad.src = imageSource;

            });

        }

        if (_.options.centerMode === true) {
            if (_.options.infinite === true) {
                rangeStart = _.currentSlide + (_.options.slidesToShow / 2 + 1);
                rangeEnd = rangeStart + _.options.slidesToShow + 2;
            } else {
                rangeStart = Math.max(0, _.currentSlide - (_.options.slidesToShow / 2 + 1));
                rangeEnd = 2 + (_.options.slidesToShow / 2 + 1) + _.currentSlide;
            }
        } else {
            rangeStart = _.options.infinite ? _.options.slidesToShow + _.currentSlide : _.currentSlide;
            rangeEnd = Math.ceil(rangeStart + _.options.slidesToShow);
            if (_.options.fade === true) {
                if (rangeStart > 0) rangeStart--;
                if (rangeEnd <= _.slideCount) rangeEnd++;
            }
        }

        loadRange = _.$slider.find('.slick-slide').slice(rangeStart, rangeEnd);
        loadImages(loadRange);

        if (_.slideCount <= _.options.slidesToShow) {
            cloneRange = _.$slider.find('.slick-slide');
            loadImages(cloneRange);
        } else
        if (_.currentSlide >= _.slideCount - _.options.slidesToShow) {
            cloneRange = _.$slider.find('.slick-cloned').slice(0, _.options.slidesToShow);
            loadImages(cloneRange);
        } else if (_.currentSlide === 0) {
            cloneRange = _.$slider.find('.slick-cloned').slice(_.options.slidesToShow * -1);
            loadImages(cloneRange);
        }

    };

    Slick.prototype.loadSlider = function() {

        var _ = this;

        _.setPosition();

        _.$slideTrack.css({
            opacity: 1
        });

        _.$slider.removeClass('slick-loading');

        _.initUI();

        if (_.options.lazyLoad === 'progressive') {
            _.progressiveLazyLoad();
        }

    };

    Slick.prototype.next = Slick.prototype.slickNext = function() {

        var _ = this;

        _.changeSlide({
            data: {
                message: 'next'
            }
        });

    };

    Slick.prototype.orientationChange = function() {

        var _ = this;

        _.checkResponsive();
        _.setPosition();

    };

    Slick.prototype.pause = Slick.prototype.slickPause = function() {

        var _ = this;

        _.autoPlayClear();
        _.paused = true;

    };

    Slick.prototype.play = Slick.prototype.slickPlay = function() {

        var _ = this;

        _.autoPlay();
        _.options.autoplay = true;
        _.paused = false;
        _.focussed = false;
        _.interrupted = false;

    };

    Slick.prototype.postSlide = function(index) {

        var _ = this;

        if( !_.unslicked ) {

            _.$slider.trigger('afterChange', [_, index]);

            _.animating = false;

            _.setPosition();

            _.swipeLeft = null;

            if ( _.options.autoplay ) {
                _.autoPlay();
            }

            if (_.options.accessibility === true) {
                _.initADA();
            }

        }

    };

    Slick.prototype.prev = Slick.prototype.slickPrev = function() {

        var _ = this;

        _.changeSlide({
            data: {
                message: 'previous'
            }
        });

    };

    Slick.prototype.preventDefault = function(event) {

        event.preventDefault();

    };

    Slick.prototype.progressiveLazyLoad = function( tryCount ) {

        tryCount = tryCount || 1;

        var _ = this,
            $imgsToLoad = $( 'img[data-lazy]', _.$slider ),
            image,
            imageSource,
            imageToLoad;

        if ( $imgsToLoad.length ) {

            image = $imgsToLoad.first();
            imageSource = image.attr('data-lazy');
            imageToLoad = document.createElement('img');

            imageToLoad.onload = function() {

                image
                    .attr( 'src', imageSource )
                    .removeAttr('data-lazy')
                    .removeClass('slick-loading');

                if ( _.options.adaptiveHeight === true ) {
                    _.setPosition();
                }

                _.$slider.trigger('lazyLoaded', [ _, image, imageSource ]);
                _.progressiveLazyLoad();

            };

            imageToLoad.onerror = function() {

                if ( tryCount < 3 ) {

                    /**
                     * try to load the image 3 times,
                     * leave a slight delay so we don't get
                     * servers blocking the request.
                     */
                    setTimeout( function() {
                        _.progressiveLazyLoad( tryCount + 1 );
                    }, 500 );

                } else {

                    image
                        .removeAttr( 'data-lazy' )
                        .removeClass( 'slick-loading' )
                        .addClass( 'slick-lazyload-error' );

                    _.$slider.trigger('lazyLoadError', [ _, image, imageSource ]);

                    _.progressiveLazyLoad();

                }

            };

            imageToLoad.src = imageSource;

        } else {

            _.$slider.trigger('allImagesLoaded', [ _ ]);

        }

    };

    Slick.prototype.refresh = function( initializing ) {

        var _ = this, currentSlide, lastVisibleIndex;

        lastVisibleIndex = _.slideCount - _.options.slidesToShow;

        // in non-infinite sliders, we don't want to go past the
        // last visible index.
        if( !_.options.infinite && ( _.currentSlide > lastVisibleIndex )) {
            _.currentSlide = lastVisibleIndex;
        }

        // if less slides than to show, go to start.
        if ( _.slideCount <= _.options.slidesToShow ) {
            _.currentSlide = 0;

        }

        currentSlide = _.currentSlide;

        _.destroy(true);

        $.extend(_, _.initials, { currentSlide: currentSlide });

        _.init();

        if( !initializing ) {

            _.changeSlide({
                data: {
                    message: 'index',
                    index: currentSlide
                }
            }, false);

        }

    };

    Slick.prototype.registerBreakpoints = function() {

        var _ = this, breakpoint, currentBreakpoint, l,
            responsiveSettings = _.options.responsive || null;

        if ( $.type(responsiveSettings) === 'array' && responsiveSettings.length ) {

            _.respondTo = _.options.respondTo || 'window';

            for ( breakpoint in responsiveSettings ) {

                l = _.breakpoints.length-1;
                currentBreakpoint = responsiveSettings[breakpoint].breakpoint;

                if (responsiveSettings.hasOwnProperty(breakpoint)) {

                    // loop through the breakpoints and cut out any existing
                    // ones with the same breakpoint number, we don't want dupes.
                    while( l >= 0 ) {
                        if( _.breakpoints[l] && _.breakpoints[l] === currentBreakpoint ) {
                            _.breakpoints.splice(l,1);
                        }
                        l--;
                    }

                    _.breakpoints.push(currentBreakpoint);
                    _.breakpointSettings[currentBreakpoint] = responsiveSettings[breakpoint].settings;

                }

            }

            _.breakpoints.sort(function(a, b) {
                return ( _.options.mobileFirst ) ? a-b : b-a;
            });

        }

    };

    Slick.prototype.reinit = function() {

        var _ = this;

        _.$slides =
            _.$slideTrack
                .children(_.options.slide)
                .addClass('slick-slide');

        _.slideCount = _.$slides.length;

        if (_.currentSlide >= _.slideCount && _.currentSlide !== 0) {
            _.currentSlide = _.currentSlide - _.options.slidesToScroll;
        }

        if (_.slideCount <= _.options.slidesToShow) {
            _.currentSlide = 0;
        }

        _.registerBreakpoints();

        _.setProps();
        _.setupInfinite();
        _.buildArrows();
        _.updateArrows();
        _.initArrowEvents();
        _.buildDots();
        _.updateDots();
        _.initDotEvents();
        _.cleanUpSlideEvents();
        _.initSlideEvents();

        _.checkResponsive(false, true);

        if (_.options.focusOnSelect === true) {
            $(_.$slideTrack).children().on('click.slick', _.selectHandler);
        }

        _.setSlideClasses(typeof _.currentSlide === 'number' ? _.currentSlide : 0);

        _.setPosition();
        _.focusHandler();

        _.paused = !_.options.autoplay;
        _.autoPlay();

        _.$slider.trigger('reInit', [_]);

    };

    Slick.prototype.resize = function() {

        var _ = this;

        if ($(window).width() !== _.windowWidth) {
            clearTimeout(_.windowDelay);
            _.windowDelay = window.setTimeout(function() {
                _.windowWidth = $(window).width();
                _.checkResponsive();
                if( !_.unslicked ) { _.setPosition(); }
            }, 50);
        }
    };

    Slick.prototype.removeSlide = Slick.prototype.slickRemove = function(index, removeBefore, removeAll) {

        var _ = this;

        if (typeof(index) === 'boolean') {
            removeBefore = index;
            index = removeBefore === true ? 0 : _.slideCount - 1;
        } else {
            index = removeBefore === true ? --index : index;
        }

        if (_.slideCount < 1 || index < 0 || index > _.slideCount - 1) {
            return false;
        }

        _.unload();

        if (removeAll === true) {
            _.$slideTrack.children().remove();
        } else {
            _.$slideTrack.children(this.options.slide).eq(index).remove();
        }

        _.$slides = _.$slideTrack.children(this.options.slide);

        _.$slideTrack.children(this.options.slide).detach();

        _.$slideTrack.append(_.$slides);

        _.$slidesCache = _.$slides;

        _.reinit();

    };

    Slick.prototype.setCSS = function(position) {

        var _ = this,
            positionProps = {},
            x, y;

        if (_.options.rtl === true) {
            position = -position;
        }
        x = _.positionProp == 'left' ? Math.ceil(position) + 'px' : '0px';
        y = _.positionProp == 'top' ? Math.ceil(position) + 'px' : '0px';

        positionProps[_.positionProp] = position;

        if (_.transformsEnabled === false) {
            _.$slideTrack.css(positionProps);
        } else {
            positionProps = {};
            if (_.cssTransitions === false) {
                positionProps[_.animType] = 'translate(' + x + ', ' + y + ')';
                _.$slideTrack.css(positionProps);
            } else {
                positionProps[_.animType] = 'translate3d(' + x + ', ' + y + ', 0px)';
                _.$slideTrack.css(positionProps);
            }
        }

    };

    Slick.prototype.setDimensions = function() {

        var _ = this;

        if (_.options.vertical === false) {
            if (_.options.centerMode === true) {
                _.$list.css({
                    padding: ('0px ' + _.options.centerPadding)
                });
            }
        } else {
            _.$list.height(_.$slides.first().outerHeight(true) * _.options.slidesToShow);
            if (_.options.centerMode === true) {
                _.$list.css({
                    padding: (_.options.centerPadding + ' 0px')
                });
            }
        }

        _.listWidth = _.$list.width();
        _.listHeight = _.$list.height();


        if (_.options.vertical === false && _.options.variableWidth === false) {
            _.slideWidth = Math.ceil(_.listWidth / _.options.slidesToShow);
            _.$slideTrack.width(Math.ceil((_.slideWidth * _.$slideTrack.children('.slick-slide').length)));

        } else if (_.options.variableWidth === true) {
            _.$slideTrack.width(5000 * _.slideCount);
        } else {
            _.slideWidth = Math.ceil(_.listWidth);
            _.$slideTrack.height(Math.ceil((_.$slides.first().outerHeight(true) * _.$slideTrack.children('.slick-slide').length)));
        }

        var offset = _.$slides.first().outerWidth(true) - _.$slides.first().width();
        if (_.options.variableWidth === false) _.$slideTrack.children('.slick-slide').width(_.slideWidth - offset);

    };

    Slick.prototype.setFade = function() {

        var _ = this,
            targetLeft;

        _.$slides.each(function(index, element) {
            targetLeft = (_.slideWidth * index) * -1;
            if (_.options.rtl === true) {
                $(element).css({
                    position: 'relative',
                    right: targetLeft,
                    top: 0,
                    zIndex: _.options.zIndex - 2,
                    opacity: 0
                });
            } else {
                $(element).css({
                    position: 'relative',
                    left: targetLeft,
                    top: 0,
                    zIndex: _.options.zIndex - 2,
                    opacity: 0
                });
            }
        });

        _.$slides.eq(_.currentSlide).css({
            zIndex: _.options.zIndex - 1,
            opacity: 1
        });

    };

    Slick.prototype.setHeight = function() {

        var _ = this;

        if (_.options.slidesToShow === 1 && _.options.adaptiveHeight === true && _.options.vertical === false) {
            var targetHeight = _.$slides.eq(_.currentSlide).outerHeight(true);
            _.$list.css('height', targetHeight);
        }

    };

    Slick.prototype.setOption =
    Slick.prototype.slickSetOption = function() {

        /**
         * accepts arguments in format of:
         *
         *  - for changing a single option's value:
         *     .slick("setOption", option, value, refresh )
         *
         *  - for changing a set of responsive options:
         *     .slick("setOption", 'responsive', [{}, ...], refresh )
         *
         *  - for updating multiple values at once (not responsive)
         *     .slick("setOption", { 'option': value, ... }, refresh )
         */

        var _ = this, l, item, option, value, refresh = false, type;

        if( $.type( arguments[0] ) === 'object' ) {

            option =  arguments[0];
            refresh = arguments[1];
            type = 'multiple';

        } else if ( $.type( arguments[0] ) === 'string' ) {

            option =  arguments[0];
            value = arguments[1];
            refresh = arguments[2];

            if ( arguments[0] === 'responsive' && $.type( arguments[1] ) === 'array' ) {

                type = 'responsive';

            } else if ( typeof arguments[1] !== 'undefined' ) {

                type = 'single';

            }

        }

        if ( type === 'single' ) {

            _.options[option] = value;


        } else if ( type === 'multiple' ) {

            $.each( option , function( opt, val ) {

                _.options[opt] = val;

            });


        } else if ( type === 'responsive' ) {

            for ( item in value ) {

                if( $.type( _.options.responsive ) !== 'array' ) {

                    _.options.responsive = [ value[item] ];

                } else {

                    l = _.options.responsive.length-1;

                    // loop through the responsive object and splice out duplicates.
                    while( l >= 0 ) {

                        if( _.options.responsive[l].breakpoint === value[item].breakpoint ) {

                            _.options.responsive.splice(l,1);

                        }

                        l--;

                    }

                    _.options.responsive.push( value[item] );

                }

            }

        }

        if ( refresh ) {

            _.unload();
            _.reinit();

        }

    };

    Slick.prototype.setPosition = function() {

        var _ = this;

        _.setDimensions();

        _.setHeight();

        if (_.options.fade === false) {
            _.setCSS(_.getLeft(_.currentSlide));
        } else {
            _.setFade();
        }

        _.$slider.trigger('setPosition', [_]);

    };

    Slick.prototype.setProps = function() {

        var _ = this,
            bodyStyle = document.body.style;

        _.positionProp = _.options.vertical === true ? 'top' : 'left';

        if (_.positionProp === 'top') {
            _.$slider.addClass('slick-vertical');
        } else {
            _.$slider.removeClass('slick-vertical');
        }

        if (bodyStyle.WebkitTransition !== undefined ||
            bodyStyle.MozTransition !== undefined ||
            bodyStyle.msTransition !== undefined) {
            if (_.options.useCSS === true) {
                _.cssTransitions = true;
            }
        }

        if ( _.options.fade ) {
            if ( typeof _.options.zIndex === 'number' ) {
                if( _.options.zIndex < 3 ) {
                    _.options.zIndex = 3;
                }
            } else {
                _.options.zIndex = _.defaults.zIndex;
            }
        }

        if (bodyStyle.OTransform !== undefined) {
            _.animType = 'OTransform';
            _.transformType = '-o-transform';
            _.transitionType = 'OTransition';
            if (bodyStyle.perspectiveProperty === undefined && bodyStyle.webkitPerspective === undefined) _.animType = false;
        }
        if (bodyStyle.MozTransform !== undefined) {
            _.animType = 'MozTransform';
            _.transformType = '-moz-transform';
            _.transitionType = 'MozTransition';
            if (bodyStyle.perspectiveProperty === undefined && bodyStyle.MozPerspective === undefined) _.animType = false;
        }
        if (bodyStyle.webkitTransform !== undefined) {
            _.animType = 'webkitTransform';
            _.transformType = '-webkit-transform';
            _.transitionType = 'webkitTransition';
            if (bodyStyle.perspectiveProperty === undefined && bodyStyle.webkitPerspective === undefined) _.animType = false;
        }
        if (bodyStyle.msTransform !== undefined) {
            _.animType = 'msTransform';
            _.transformType = '-ms-transform';
            _.transitionType = 'msTransition';
            if (bodyStyle.msTransform === undefined) _.animType = false;
        }
        if (bodyStyle.transform !== undefined && _.animType !== false) {
            _.animType = 'transform';
            _.transformType = 'transform';
            _.transitionType = 'transition';
        }
        _.transformsEnabled = _.options.useTransform && (_.animType !== null && _.animType !== false);
    };


    Slick.prototype.setSlideClasses = function(index) {

        var _ = this,
            centerOffset, allSlides, indexOffset, remainder;

        allSlides = _.$slider
            .find('.slick-slide')
            .removeClass('slick-active slick-center slick-current')
            .attr('aria-hidden', 'true');

        _.$slides
            .eq(index)
            .addClass('slick-current');

        if (_.options.centerMode === true) {

            centerOffset = Math.floor(_.options.slidesToShow / 2);

            if (_.options.infinite === true) {

                if (index >= centerOffset && index <= (_.slideCount - 1) - centerOffset) {

                    _.$slides
                        .slice(index - centerOffset, index + centerOffset + 1)
                        .addClass('slick-active')
                        .attr('aria-hidden', 'false');

                } else {

                    indexOffset = _.options.slidesToShow + index;
                    allSlides
                        .slice(indexOffset - centerOffset + 1, indexOffset + centerOffset + 2)
                        .addClass('slick-active')
                        .attr('aria-hidden', 'false');

                }

                if (index === 0) {

                    allSlides
                        .eq(allSlides.length - 1 - _.options.slidesToShow)
                        .addClass('slick-center');

                } else if (index === _.slideCount - 1) {

                    allSlides
                        .eq(_.options.slidesToShow)
                        .addClass('slick-center');

                }

            }

            _.$slides
                .eq(index)
                .addClass('slick-center');

        } else {

            if (index >= 0 && index <= (_.slideCount - _.options.slidesToShow)) {

                _.$slides
                    .slice(index, index + _.options.slidesToShow)
                    .addClass('slick-active')
                    .attr('aria-hidden', 'false');

            } else if (allSlides.length <= _.options.slidesToShow) {

                allSlides
                    .addClass('slick-active')
                    .attr('aria-hidden', 'false');

            } else {

                remainder = _.slideCount % _.options.slidesToShow;
                indexOffset = _.options.infinite === true ? _.options.slidesToShow + index : index;

                if (_.options.slidesToShow == _.options.slidesToScroll && (_.slideCount - index) < _.options.slidesToShow) {

                    allSlides
                        .slice(indexOffset - (_.options.slidesToShow - remainder), indexOffset + remainder)
                        .addClass('slick-active')
                        .attr('aria-hidden', 'false');

                } else {

                    allSlides
                        .slice(indexOffset, indexOffset + _.options.slidesToShow)
                        .addClass('slick-active')
                        .attr('aria-hidden', 'false');

                }

            }

        }

        if (_.options.lazyLoad === 'ondemand') {
            _.lazyLoad();
        }

    };

    Slick.prototype.setupInfinite = function() {

        var _ = this,
            i, slideIndex, infiniteCount;

        if (_.options.fade === true) {
            _.options.centerMode = false;
        }

        if (_.options.infinite === true && _.options.fade === false) {

            slideIndex = null;

            if (_.slideCount > _.options.slidesToShow) {

                if (_.options.centerMode === true) {
                    infiniteCount = _.options.slidesToShow + 1;
                } else {
                    infiniteCount = _.options.slidesToShow;
                }

                for (i = _.slideCount; i > (_.slideCount -
                        infiniteCount); i -= 1) {
                    slideIndex = i - 1;
                    $(_.$slides[slideIndex]).clone(true).attr('id', '')
                        .attr('data-slick-index', slideIndex - _.slideCount)
                        .prependTo(_.$slideTrack).addClass('slick-cloned');
                }
                for (i = 0; i < infiniteCount; i += 1) {
                    slideIndex = i;
                    $(_.$slides[slideIndex]).clone(true).attr('id', '')
                        .attr('data-slick-index', slideIndex + _.slideCount)
                        .appendTo(_.$slideTrack).addClass('slick-cloned');
                }
                _.$slideTrack.find('.slick-cloned').find('[id]').each(function() {
                    $(this).attr('id', '');
                });

            }

        }

    };

    Slick.prototype.interrupt = function( toggle ) {

        var _ = this;

        if( !toggle ) {
            _.autoPlay();
        }
        _.interrupted = toggle;

    };

    Slick.prototype.selectHandler = function(event) {

        var _ = this;

        var targetElement =
            $(event.target).is('.slick-slide') ?
                $(event.target) :
                $(event.target).parents('.slick-slide');

        var index = parseInt(targetElement.attr('data-slick-index'));

        if (!index) index = 0;

        if (_.slideCount <= _.options.slidesToShow) {

            _.setSlideClasses(index);
            _.asNavFor(index);
            return;

        }

        _.slideHandler(index);

    };

    Slick.prototype.slideHandler = function(index, sync, dontAnimate) {

        var targetSlide, animSlide, oldSlide, slideLeft, targetLeft = null,
            _ = this, navTarget;

        sync = sync || false;

        if (_.animating === true && _.options.waitForAnimate === true) {
            return;
        }

        if (_.options.fade === true && _.currentSlide === index) {
            return;
        }

        if (_.slideCount <= _.options.slidesToShow) {
            return;
        }

        if (sync === false) {
            _.asNavFor(index);
        }

        targetSlide = index;
        targetLeft = _.getLeft(targetSlide);
        slideLeft = _.getLeft(_.currentSlide);

        _.currentLeft = _.swipeLeft === null ? slideLeft : _.swipeLeft;

        if (_.options.infinite === false && _.options.centerMode === false && (index < 0 || index > _.getDotCount() * _.options.slidesToScroll)) {
            if (_.options.fade === false) {
                targetSlide = _.currentSlide;
                if (dontAnimate !== true) {
                    _.animateSlide(slideLeft, function() {
                        _.postSlide(targetSlide);
                    });
                } else {
                    _.postSlide(targetSlide);
                }
            }
            return;
        } else if (_.options.infinite === false && _.options.centerMode === true && (index < 0 || index > (_.slideCount - _.options.slidesToScroll))) {
            if (_.options.fade === false) {
                targetSlide = _.currentSlide;
                if (dontAnimate !== true) {
                    _.animateSlide(slideLeft, function() {
                        _.postSlide(targetSlide);
                    });
                } else {
                    _.postSlide(targetSlide);
                }
            }
            return;
        }

        if ( _.options.autoplay ) {
            clearInterval(_.autoPlayTimer);
        }

        if (targetSlide < 0) {
            if (_.slideCount % _.options.slidesToScroll !== 0) {
                animSlide = _.slideCount - (_.slideCount % _.options.slidesToScroll);
            } else {
                animSlide = _.slideCount + targetSlide;
            }
        } else if (targetSlide >= _.slideCount) {
            if (_.slideCount % _.options.slidesToScroll !== 0) {
                animSlide = 0;
            } else {
                animSlide = targetSlide - _.slideCount;
            }
        } else {
            animSlide = targetSlide;
        }

        _.animating = true;

        _.$slider.trigger('beforeChange', [_, _.currentSlide, animSlide]);

        oldSlide = _.currentSlide;
        _.currentSlide = animSlide;

        _.setSlideClasses(_.currentSlide);

        if ( _.options.asNavFor ) {

            navTarget = _.getNavTarget();
            navTarget = navTarget.slick('getSlick');

            if ( navTarget.slideCount <= navTarget.options.slidesToShow ) {
                navTarget.setSlideClasses(_.currentSlide);
            }

        }

        _.updateDots();
        _.updateArrows();

        if (_.options.fade === true) {
            if (dontAnimate !== true) {

                _.fadeSlideOut(oldSlide);

                _.fadeSlide(animSlide, function() {
                    _.postSlide(animSlide);
                });

            } else {
                _.postSlide(animSlide);
            }
            _.animateHeight();
            return;
        }

        if (dontAnimate !== true) {
            _.animateSlide(targetLeft, function() {
                _.postSlide(animSlide);
            });
        } else {
            _.postSlide(animSlide);
        }

    };

    Slick.prototype.startLoad = function() {

        var _ = this;

        if (_.options.arrows === true && _.slideCount > _.options.slidesToShow) {

            _.$prevArrow.hide();
            _.$nextArrow.hide();

        }

        if (_.options.dots === true && _.slideCount > _.options.slidesToShow) {

            _.$dots.hide();

        }

        _.$slider.addClass('slick-loading');

    };

    Slick.prototype.swipeDirection = function() {

        var xDist, yDist, r, swipeAngle, _ = this;

        xDist = _.touchObject.startX - _.touchObject.curX;
        yDist = _.touchObject.startY - _.touchObject.curY;
        r = Math.atan2(yDist, xDist);

        swipeAngle = Math.round(r * 180 / Math.PI);
        if (swipeAngle < 0) {
            swipeAngle = 360 - Math.abs(swipeAngle);
        }

        if ((swipeAngle <= 45) && (swipeAngle >= 0)) {
            return (_.options.rtl === false ? 'left' : 'right');
        }
        if ((swipeAngle <= 360) && (swipeAngle >= 315)) {
            return (_.options.rtl === false ? 'left' : 'right');
        }
        if ((swipeAngle >= 135) && (swipeAngle <= 225)) {
            return (_.options.rtl === false ? 'right' : 'left');
        }
        if (_.options.verticalSwiping === true) {
            if ((swipeAngle >= 35) && (swipeAngle <= 135)) {
                return 'down';
            } else {
                return 'up';
            }
        }

        return 'vertical';

    };

    Slick.prototype.swipeEnd = function(event) {

        var _ = this,
            slideCount,
            direction;

        _.dragging = false;
        _.interrupted = false;
        _.shouldClick = ( _.touchObject.swipeLength > 10 ) ? false : true;

        if ( _.touchObject.curX === undefined ) {
            return false;
        }

        if ( _.touchObject.edgeHit === true ) {
            _.$slider.trigger('edge', [_, _.swipeDirection() ]);
        }

        if ( _.touchObject.swipeLength >= _.touchObject.minSwipe ) {

            direction = _.swipeDirection();

            switch ( direction ) {

                case 'left':
                case 'down':

                    slideCount =
                        _.options.swipeToSlide ?
                            _.checkNavigable( _.currentSlide + _.getSlideCount() ) :
                            _.currentSlide + _.getSlideCount();

                    _.currentDirection = 0;

                    break;

                case 'right':
                case 'up':

                    slideCount =
                        _.options.swipeToSlide ?
                            _.checkNavigable( _.currentSlide - _.getSlideCount() ) :
                            _.currentSlide - _.getSlideCount();

                    _.currentDirection = 1;

                    break;

                default:


            }

            if( direction != 'vertical' ) {

                _.slideHandler( slideCount );
                _.touchObject = {};
                _.$slider.trigger('swipe', [_, direction ]);

            }

        } else {

            if ( _.touchObject.startX !== _.touchObject.curX ) {

                _.slideHandler( _.currentSlide );
                _.touchObject = {};

            }

        }

    };

    Slick.prototype.swipeHandler = function(event) {

        var _ = this;

        if ((_.options.swipe === false) || ('ontouchend' in document && _.options.swipe === false)) {
            return;
        } else if (_.options.draggable === false && event.type.indexOf('mouse') !== -1) {
            return;
        }

        _.touchObject.fingerCount = event.originalEvent && event.originalEvent.touches !== undefined ?
            event.originalEvent.touches.length : 1;

        _.touchObject.minSwipe = _.listWidth / _.options
            .touchThreshold;

        if (_.options.verticalSwiping === true) {
            _.touchObject.minSwipe = _.listHeight / _.options
                .touchThreshold;
        }

        switch (event.data.action) {

            case 'start':
                _.swipeStart(event);
                break;

            case 'move':
                _.swipeMove(event);
                break;

            case 'end':
                _.swipeEnd(event);
                break;

        }

    };

    Slick.prototype.swipeMove = function(event) {

        var _ = this,
            edgeWasHit = false,
            curLeft, swipeDirection, swipeLength, positionOffset, touches;

        touches = event.originalEvent !== undefined ? event.originalEvent.touches : null;

        if (!_.dragging || touches && touches.length !== 1) {
            return false;
        }

        curLeft = _.getLeft(_.currentSlide);

        _.touchObject.curX = touches !== undefined ? touches[0].pageX : event.clientX;
        _.touchObject.curY = touches !== undefined ? touches[0].pageY : event.clientY;

        _.touchObject.swipeLength = Math.round(Math.sqrt(
            Math.pow(_.touchObject.curX - _.touchObject.startX, 2)));

        if (_.options.verticalSwiping === true) {
            _.touchObject.swipeLength = Math.round(Math.sqrt(
                Math.pow(_.touchObject.curY - _.touchObject.startY, 2)));
        }

        swipeDirection = _.swipeDirection();

        if (swipeDirection === 'vertical') {
            return;
        }

        if (event.originalEvent !== undefined && _.touchObject.swipeLength > 4) {
            event.preventDefault();
        }

        positionOffset = (_.options.rtl === false ? 1 : -1) * (_.touchObject.curX > _.touchObject.startX ? 1 : -1);
        if (_.options.verticalSwiping === true) {
            positionOffset = _.touchObject.curY > _.touchObject.startY ? 1 : -1;
        }


        swipeLength = _.touchObject.swipeLength;

        _.touchObject.edgeHit = false;

        if (_.options.infinite === false) {
            if ((_.currentSlide === 0 && swipeDirection === 'right') || (_.currentSlide >= _.getDotCount() && swipeDirection === 'left')) {
                swipeLength = _.touchObject.swipeLength * _.options.edgeFriction;
                _.touchObject.edgeHit = true;
            }
        }

        if (_.options.vertical === false) {
            _.swipeLeft = curLeft + swipeLength * positionOffset;
        } else {
            _.swipeLeft = curLeft + (swipeLength * (_.$list.height() / _.listWidth)) * positionOffset;
        }
        if (_.options.verticalSwiping === true) {
            _.swipeLeft = curLeft + swipeLength * positionOffset;
        }

        if (_.options.fade === true || _.options.touchMove === false) {
            return false;
        }

        if (_.animating === true) {
            _.swipeLeft = null;
            return false;
        }

        _.setCSS(_.swipeLeft);

    };

    Slick.prototype.swipeStart = function(event) {

        var _ = this,
            touches;

        _.interrupted = true;

        if (_.touchObject.fingerCount !== 1 || _.slideCount <= _.options.slidesToShow) {
            _.touchObject = {};
            return false;
        }

        if (event.originalEvent !== undefined && event.originalEvent.touches !== undefined) {
            touches = event.originalEvent.touches[0];
        }

        _.touchObject.startX = _.touchObject.curX = touches !== undefined ? touches.pageX : event.clientX;
        _.touchObject.startY = _.touchObject.curY = touches !== undefined ? touches.pageY : event.clientY;

        _.dragging = true;

    };

    Slick.prototype.unfilterSlides = Slick.prototype.slickUnfilter = function() {

        var _ = this;

        if (_.$slidesCache !== null) {

            _.unload();

            _.$slideTrack.children(this.options.slide).detach();

            _.$slidesCache.appendTo(_.$slideTrack);

            _.reinit();

        }

    };

    Slick.prototype.unload = function() {

        var _ = this;

        $('.slick-cloned', _.$slider).remove();

        if (_.$dots) {
            _.$dots.remove();
        }

        if (_.$prevArrow && _.htmlExpr.test(_.options.prevArrow)) {
            _.$prevArrow.remove();
        }

        if (_.$nextArrow && _.htmlExpr.test(_.options.nextArrow)) {
            _.$nextArrow.remove();
        }

        _.$slides
            .removeClass('slick-slide slick-active slick-visible slick-current')
            .attr('aria-hidden', 'true')
            .css('width', '');

    };

    Slick.prototype.unslick = function(fromBreakpoint) {

        var _ = this;
        _.$slider.trigger('unslick', [_, fromBreakpoint]);
        _.destroy();

    };

    Slick.prototype.updateArrows = function() {

        var _ = this,
            centerOffset;

        centerOffset = Math.floor(_.options.slidesToShow / 2);

        if ( _.options.arrows === true &&
            _.slideCount > _.options.slidesToShow &&
            !_.options.infinite ) {

            _.$prevArrow.removeClass('slick-disabled').attr('aria-disabled', 'false');
            _.$nextArrow.removeClass('slick-disabled').attr('aria-disabled', 'false');

            if (_.currentSlide === 0) {

                _.$prevArrow.addClass('slick-disabled').attr('aria-disabled', 'true');
                _.$nextArrow.removeClass('slick-disabled').attr('aria-disabled', 'false');

            } else if (_.currentSlide >= _.slideCount - _.options.slidesToShow && _.options.centerMode === false) {

                _.$nextArrow.addClass('slick-disabled').attr('aria-disabled', 'true');
                _.$prevArrow.removeClass('slick-disabled').attr('aria-disabled', 'false');

            } else if (_.currentSlide >= _.slideCount - 1 && _.options.centerMode === true) {

                _.$nextArrow.addClass('slick-disabled').attr('aria-disabled', 'true');
                _.$prevArrow.removeClass('slick-disabled').attr('aria-disabled', 'false');

            }

        }

    };

    Slick.prototype.updateDots = function() {

        var _ = this;

        if (_.$dots !== null) {

            _.$dots
                .find('li')
                .removeClass('slick-active')
                .attr('aria-hidden', 'true');

            _.$dots
                .find('li')
                .eq(Math.floor(_.currentSlide / _.options.slidesToScroll))
                .addClass('slick-active')
                .attr('aria-hidden', 'false');

        }

    };

    Slick.prototype.visibility = function() {

        var _ = this;

        if ( _.options.autoplay ) {

            if ( document[_.hidden] ) {

                _.interrupted = true;

            } else {

                _.interrupted = false;

            }

        }

    };

    $.fn.slick = function() {
        var _ = this,
            opt = arguments[0],
            args = Array.prototype.slice.call(arguments, 1),
            l = _.length,
            i,
            ret;
        for (i = 0; i < l; i++) {
            if (typeof opt == 'object' || typeof opt == 'undefined')
                _[i].slick = new Slick(_[i], opt);
            else
                ret = _[i].slick[opt].apply(_[i].slick, args);
            if (typeof ret != 'undefined') return ret;
        }
        return _;
    };

}));

/*!
 * angular-slick-carousel
 * DevMark <hc.devmark@gmail.com>
 * https://github.com/devmark/angular-slick-carousel
 * Version: 3.1.7 - 2016-08-04T06:17:55.528Z
 * License: MIT
 */


'use strict';

angular
  .module('slickCarousel', [])
  //global config
  .constant('slickCarouselConfig', {
    method: {},
    event: {}
  })
  .directive('slick', [
    '$timeout', 'slickCarouselConfig', function ($timeout, slickCarouselConfig) {
      var slickMethodList, slickEventList;
      slickMethodList = ['slickGoTo', 'slickNext', 'slickPrev', 'slickPause', 'slickPlay', 'slickAdd', 'slickRemove', 'slickFilter', 'slickUnfilter', 'unslick'];
      slickEventList = ['afterChange', 'beforeChange', 'breakpoint', 'destroy', 'edge', 'init', 'reInit', 'setPosition', 'swipe', 'lazyLoaded', 'lazyLoadError'];

      return {
        scope: {
          settings: '=',
          enabled: '@',
          accessibility: '@',
          adaptiveHeight: '@',
          autoplay: '@',
          autoplaySpeed: '@',
          arrows: '@',
          asNavFor: '@',
          appendArrows: '@',
          prevArrow: '@',
          nextArrow: '@',
          centerMode: '@',
          centerPadding: '@',
          cssEase: '@',
          customPaging: '&',
          dots: '@',
          draggable: '@',
          fade: '@',
          focusOnSelect: '@',
          easing: '@',
          edgeFriction: '@',
          infinite: '@',
          initialSlide: '@',
          lazyLoad: '@',
          mobileFirst: '@',
          pauseOnHover: '@',
          pauseOnDotsHover: '@',
          respondTo: '@',
          responsive: '=?',
          rows: '@',
          slide: '@',
          slidesPerRow: '@',
          slidesToShow: '@',
          slidesToScroll: '@',
          speed: '@',
          swipe: '@',
          swipeToSlide: '@',
          touchMove: '@',
          touchThreshold: '@',
          useCSS: '@',
          variableWidth: '@',
          vertical: '@',
          verticalSwiping: '@',
          rtl: '@'
        },
        restrict: 'AE',
        link: function (scope, element, attr) {
          //hide slider
          angular.element(element).css('display', 'none');

          var options, initOptions, destroy, init, destroyAndInit, currentIndex;

          initOptions = function () {
            options = angular.extend(angular.copy(slickCarouselConfig), {
              enabled: scope.enabled !== 'false',
              accessibility: scope.accessibility !== 'false',
              adaptiveHeight: scope.adaptiveHeight === 'true',
              autoplay: scope.autoplay === 'true',
              autoplaySpeed: scope.autoplaySpeed != null ? parseInt(scope.autoplaySpeed, 10) : 3000,
              arrows: scope.arrows !== 'false',
              asNavFor: scope.asNavFor ? scope.asNavFor : void 0,
              appendArrows: scope.appendArrows ? angular.element(scope.appendArrows) : angular.element(element),
              prevArrow: scope.prevArrow ? angular.element(scope.prevArrow) : void 0,
              nextArrow: scope.nextArrow ? angular.element(scope.nextArrow) : void 0,
              centerMode: scope.centerMode === 'true',
              centerPadding: scope.centerPadding || '50px',
              cssEase: scope.cssEase || 'ease',
              customPaging: attr.customPaging ? function (slick, index) {
                return scope.customPaging({slick: slick, index: index});
              } : void 0,
              dots: scope.dots === 'true',
              draggable: scope.draggable !== 'false',
              fade: scope.fade === 'true',
              focusOnSelect: scope.focusOnSelect === 'true',
              easing: scope.easing || 'linear',
              edgeFriction: scope.edgeFriction || 0.15,
              infinite: scope.infinite !== 'false',
              initialSlide: parseInt(scope.initialSlide) || 0,
              lazyLoad: scope.lazyLoad || 'ondemand',
              mobileFirst: scope.mobileFirst === 'true',
              pauseOnHover: scope.pauseOnHover !== 'false',
              pauseOnDotsHover: scope.pauseOnDotsHover === "true",
              respondTo: scope.respondTo != null ? scope.respondTo : 'window',
              responsive: scope.responsive || void 0,
              rows: scope.rows != null ? parseInt(scope.rows, 10) : 1,
              slide: scope.slide || '',
              slidesPerRow: scope.slidesPerRow != null ? parseInt(scope.slidesPerRow, 10) : 1,
              slidesToShow: scope.slidesToShow != null ? parseInt(scope.slidesToShow, 10) : 1,
              slidesToScroll: scope.slidesToScroll != null ? parseInt(scope.slidesToScroll, 10) : 1,
              speed: scope.speed != null ? parseInt(scope.speed, 10) : 300,
              swipe: scope.swipe !== 'false',
              swipeToSlide: scope.swipeToSlide === 'true',
              touchMove: scope.touchMove !== 'false',
              touchThreshold: scope.touchThreshold ? parseInt(scope.touchThreshold, 10) : 5,
              useCSS: scope.useCSS !== 'false',
              variableWidth: scope.variableWidth === 'true',
              vertical: scope.vertical === 'true',
              verticalSwiping: scope.verticalSwiping === 'true',
              rtl: scope.rtl === 'true'
            }, scope.settings);

          };

          destroy = function () {
            var slickness = angular.element(element);
            if (slickness.hasClass('slick-initialized')) {
              slickness.remove('slick-list');
              slickness.slick('unslick');
            }

            return slickness;
          };

          init = function () {
            initOptions();

            var slickness = angular.element(element);

            if (angular.element(element).hasClass('slick-initialized')) {
              if (options.enabled) {
                return slickness.slick('getSlick');
              } else {
                destroy();
              }
            } else {
              if (!options.enabled) {
                return;
              }
              // Event
              slickness.on('init', function (event, slick) {
                if (typeof options.event.init !== 'undefined') {
                  options.event.init(event, slick);
                }
                if (typeof currentIndex !== 'undefined') {
                  return slick.slideHandler(currentIndex);
                }
              });

              $timeout(function () {
                angular.element(element).css('display', 'block');
                slickness.not('.slick-initialized').slick(options);
              });
            }

            scope.internalControl = options.method || {};

            // Method
            slickMethodList.forEach(function (value) {
              scope.internalControl[value] = function () {
                var args;
                args = Array.prototype.slice.call(arguments);
                args.unshift(value);
                slickness.slick.apply(element, args);
              };
            });

            // Event
            slickness.on('afterChange', function (event, slick, currentSlide) {
              currentIndex = currentSlide;
              if (typeof options.event.afterChange !== 'undefined') {
                $timeout(function () {
                  scope.$apply(function () {
                    options.event.afterChange(event, slick, currentSlide);
                  });
                });
              }
            });

            slickness.on('beforeChange', function (event, slick, currentSlide, nextSlide) {
              if (typeof options.event.beforeChange !== 'undefined') {
                $timeout(function () {
                  $timeout(function () {
                    scope.$apply(function () {
                      options.event.beforeChange(event, slick, currentSlide, nextSlide);
                    });
                  });
                });
              }
            });

            slickness.on('reInit', function (event, slick) {
              if (typeof options.event.reInit !== 'undefined') {
                $timeout(function () {
                  scope.$apply(function () {
                    options.event.reInit(event, slick);
                  });
                });
              }
            });

            if (typeof options.event.breakpoint !== 'undefined') {
              slickness.on('breakpoint', function (event, slick, breakpoint) {
                $timeout(function () {
                  scope.$apply(function () {
                    options.event.breakpoint(event, slick, breakpoint);
                  });
                });
              });
            }
            if (typeof options.event.destroy !== 'undefined') {
              slickness.on('destroy', function (event, slick) {
                $timeout(function () {
                  scope.$apply(function () {
                    options.event.destroy(event, slick);
                  });
                });
              });
            }
            if (typeof options.event.edge !== 'undefined') {
              slickness.on('edge', function (event, slick, direction) {
                $timeout(function () {
                  scope.$apply(function () {
                    options.event.edge(event, slick, direction);
                  });
                });
              });
            }

            if (typeof options.event.setPosition !== 'undefined') {
              slickness.on('setPosition', function (event, slick) {
                $timeout(function () {
                  scope.$apply(function () {
                    options.event.setPosition(event, slick);
                  });
                });
              });
            }
            if (typeof options.event.swipe !== 'undefined') {
              slickness.on('swipe', function (event, slick, direction) {
                $timeout(function () {
                  scope.$apply(function () {
                    options.event.swipe(event, slick, direction);
                  });
                });
              });
            }
            if (typeof options.event.lazyLoaded !== 'undefined') {
              slickness.on('lazyLoaded', function (event, slick, image, imageSource) {
                $timeout(function () {
                  scope.$apply(function () {
                    options.event.lazyLoaded(event, slick, image, imageSource);
                  });
                });
              });
            }
            if (typeof options.event.lazyLoadError !== 'undefined') {
              slickness.on('lazyLoadError', function (event, slick, image, imageSource) {
                $timeout(function () {
                  scope.$apply(function () {
                    options.event.lazyLoadError(event, slick, image, imageSource);
                  });
                });
              });
            }
          };

          destroyAndInit = function () {
            destroy();
            init();
          };

          element.one('$destroy', function () {
            destroy();
          });

          return scope.$watch('settings', function (newVal, oldVal) {
            if (newVal !== null) {
              return destroyAndInit();
            }
          }, true);

        }
      };
    }
  ]);

/*!
 * angular-translate - v2.11.1 - 2016-07-17
 * 
 * Copyright (c) 2016 The angular-translate team, Pascal Precht; Licensed MIT
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module unless amdModuleId is set
    define([], function () {
      return (factory());
    });
  } else if (typeof exports === 'object') {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory();
  } else {
    factory();
  }
}(this, function () {

/**
 * @ngdoc overview
 * @name pascalprecht.translate
 *
 * @description
 * The main module which holds everything together.
 */
runTranslate.$inject = ['$translate'];
$translate.$inject = ['$STORAGE_KEY', '$windowProvider', '$translateSanitizationProvider', 'pascalprechtTranslateOverrider'];
$translateDefaultInterpolation.$inject = ['$interpolate', '$translateSanitization'];
translateDirective.$inject = ['$translate', '$q', '$interpolate', '$compile', '$parse', '$rootScope'];
translateCloakDirective.$inject = ['$translate', '$rootScope'];
translateFilterFactory.$inject = ['$parse', '$translate'];
$translationCache.$inject = ['$cacheFactory'];
angular.module('pascalprecht.translate', ['ng'])
  .run(runTranslate);

function runTranslate($translate) {

  'use strict';

  var key = $translate.storageKey(),
    storage = $translate.storage();

  var fallbackFromIncorrectStorageValue = function () {
    var preferred = $translate.preferredLanguage();
    if (angular.isString(preferred)) {
      $translate.use(preferred);
      // $translate.use() will also remember the language.
      // So, we don't need to call storage.put() here.
    } else {
      storage.put(key, $translate.use());
    }
  };

  fallbackFromIncorrectStorageValue.displayName = 'fallbackFromIncorrectStorageValue';

  if (storage) {
    if (!storage.get(key)) {
      fallbackFromIncorrectStorageValue();
    } else {
      $translate.use(storage.get(key))['catch'](fallbackFromIncorrectStorageValue);
    }
  } else if (angular.isString($translate.preferredLanguage())) {
    $translate.use($translate.preferredLanguage());
  }
}

runTranslate.displayName = 'runTranslate';

/**
 * @ngdoc object
 * @name pascalprecht.translate.$translateSanitizationProvider
 *
 * @description
 *
 * Configurations for $translateSanitization
 */
angular.module('pascalprecht.translate').provider('$translateSanitization', $translateSanitizationProvider);

function $translateSanitizationProvider () {

  'use strict';

  var $sanitize,
      currentStrategy = null, // TODO change to either 'sanitize', 'escape' or ['sanitize', 'escapeParameters'] in 3.0.
      hasConfiguredStrategy = false,
      hasShownNoStrategyConfiguredWarning = false,
      strategies;

  /**
   * Definition of a sanitization strategy function
   * @callback StrategyFunction
   * @param {string|object} value - value to be sanitized (either a string or an interpolated value map)
   * @param {string} mode - either 'text' for a string (translation) or 'params' for the interpolated params
   * @return {string|object}
   */

  /**
   * @ngdoc property
   * @name strategies
   * @propertyOf pascalprecht.translate.$translateSanitizationProvider
   *
   * @description
   * Following strategies are built-in:
   * <dl>
   *   <dt>sanitize</dt>
   *   <dd>Sanitizes HTML in the translation text using $sanitize</dd>
   *   <dt>escape</dt>
   *   <dd>Escapes HTML in the translation</dd>
   *   <dt>sanitizeParameters</dt>
   *   <dd>Sanitizes HTML in the values of the interpolation parameters using $sanitize</dd>
   *   <dt>escapeParameters</dt>
   *   <dd>Escapes HTML in the values of the interpolation parameters</dd>
   *   <dt>escaped</dt>
   *   <dd>Support legacy strategy name 'escaped' for backwards compatibility (will be removed in 3.0)</dd>
   * </dl>
   *
   */

  strategies = {
    sanitize: function (value, mode) {
      if (mode === 'text') {
        value = htmlSanitizeValue(value);
      }
      return value;
    },
    escape: function (value, mode) {
      if (mode === 'text') {
        value = htmlEscapeValue(value);
      }
      return value;
    },
    sanitizeParameters: function (value, mode) {
      if (mode === 'params') {
        value = mapInterpolationParameters(value, htmlSanitizeValue);
      }
      return value;
    },
    escapeParameters: function (value, mode) {
      if (mode === 'params') {
        value = mapInterpolationParameters(value, htmlEscapeValue);
      }
      return value;
    }
  };
  // Support legacy strategy name 'escaped' for backwards compatibility.
  // TODO should be removed in 3.0
  strategies.escaped = strategies.escapeParameters;

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateSanitizationProvider#addStrategy
   * @methodOf pascalprecht.translate.$translateSanitizationProvider
   *
   * @description
   * Adds a sanitization strategy to the list of known strategies.
   *
   * @param {string} strategyName - unique key for a strategy
   * @param {StrategyFunction} strategyFunction - strategy function
   * @returns {object} this
   */
  this.addStrategy = function (strategyName, strategyFunction) {
    strategies[strategyName] = strategyFunction;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateSanitizationProvider#removeStrategy
   * @methodOf pascalprecht.translate.$translateSanitizationProvider
   *
   * @description
   * Removes a sanitization strategy from the list of known strategies.
   *
   * @param {string} strategyName - unique key for a strategy
   * @returns {object} this
   */
  this.removeStrategy = function (strategyName) {
    delete strategies[strategyName];
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateSanitizationProvider#useStrategy
   * @methodOf pascalprecht.translate.$translateSanitizationProvider
   *
   * @description
   * Selects a sanitization strategy. When an array is provided the strategies will be executed in order.
   *
   * @param {string|StrategyFunction|array} strategy The sanitization strategy / strategies which should be used. Either a name of an existing strategy, a custom strategy function, or an array consisting of multiple names and / or custom functions.
   * @returns {object} this
   */
  this.useStrategy = function (strategy) {
    hasConfiguredStrategy = true;
    currentStrategy = strategy;
    return this;
  };

  /**
   * @ngdoc object
   * @name pascalprecht.translate.$translateSanitization
   * @requires $injector
   * @requires $log
   *
   * @description
   * Sanitizes interpolation parameters and translated texts.
   *
   */
  this.$get = ['$injector', '$log', function ($injector, $log) {

    var cachedStrategyMap = {};

    var applyStrategies = function (value, mode, selectedStrategies) {
      angular.forEach(selectedStrategies, function (selectedStrategy) {
        if (angular.isFunction(selectedStrategy)) {
          value = selectedStrategy(value, mode);
        } else if (angular.isFunction(strategies[selectedStrategy])) {
          value = strategies[selectedStrategy](value, mode);
        } else if (angular.isString(strategies[selectedStrategy])) {
          if (!cachedStrategyMap[strategies[selectedStrategy]]) {
            try {
              cachedStrategyMap[strategies[selectedStrategy]] = $injector.get(strategies[selectedStrategy]);
            } catch (e) {
              cachedStrategyMap[strategies[selectedStrategy]] = function() {};
              throw new Error('pascalprecht.translate.$translateSanitization: Unknown sanitization strategy: \'' + selectedStrategy + '\'');
            }
          }
          value = cachedStrategyMap[strategies[selectedStrategy]](value, mode);
        } else {
          throw new Error('pascalprecht.translate.$translateSanitization: Unknown sanitization strategy: \'' + selectedStrategy + '\'');
        }
      });
      return value;
    };

    // TODO: should be removed in 3.0
    var showNoStrategyConfiguredWarning = function () {
      if (!hasConfiguredStrategy && !hasShownNoStrategyConfiguredWarning) {
        $log.warn('pascalprecht.translate.$translateSanitization: No sanitization strategy has been configured. This can have serious security implications. See http://angular-translate.github.io/docs/#/guide/19_security for details.');
        hasShownNoStrategyConfiguredWarning = true;
      }
    };

    if ($injector.has('$sanitize')) {
      $sanitize = $injector.get('$sanitize');
    }

    return {
      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translateSanitization#useStrategy
       * @methodOf pascalprecht.translate.$translateSanitization
       *
       * @description
       * Selects a sanitization strategy. When an array is provided the strategies will be executed in order.
       *
       * @param {string|StrategyFunction|array} strategy The sanitization strategy / strategies which should be used. Either a name of an existing strategy, a custom strategy function, or an array consisting of multiple names and / or custom functions.
       */
      useStrategy: (function (self) {
        return function (strategy) {
          self.useStrategy(strategy);
        };
      })(this),

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translateSanitization#sanitize
       * @methodOf pascalprecht.translate.$translateSanitization
       *
       * @description
       * Sanitizes a value.
       *
       * @param {string|object} value The value which should be sanitized.
       * @param {string} mode The current sanitization mode, either 'params' or 'text'.
       * @param {string|StrategyFunction|array} [strategy] Optional custom strategy which should be used instead of the currently selected strategy.
       * @returns {string|object} sanitized value
       */
      sanitize: function (value, mode, strategy) {
        if (!currentStrategy) {
          showNoStrategyConfiguredWarning();
        }

        if (arguments.length < 3) {
          strategy = currentStrategy;
        }

        if (!strategy) {
          return value;
        }

        var selectedStrategies = angular.isArray(strategy) ? strategy : [strategy];
        return applyStrategies(value, mode, selectedStrategies);
      }
    };
  }];

  var htmlEscapeValue = function (value) {
    var element = angular.element('<div></div>');
    element.text(value); // not chainable, see #1044
    return element.html();
  };

  var htmlSanitizeValue = function (value) {
    if (!$sanitize) {
      throw new Error('pascalprecht.translate.$translateSanitization: Error cannot find $sanitize service. Either include the ngSanitize module (https://docs.angularjs.org/api/ngSanitize) or use a sanitization strategy which does not depend on $sanitize, such as \'escape\'.');
    }
    return $sanitize(value);
  };

  var mapInterpolationParameters = function (value, iteratee, stack) {
    if (angular.isObject(value)) {
      var result = angular.isArray(value) ? [] : {};

      if (!stack) {
        stack = [];
      } else {
        if (stack.indexOf(value) > -1) {
          throw new Error('pascalprecht.translate.$translateSanitization: Error cannot interpolate parameter due recursive object');
        }
      }

      stack.push(value);
      angular.forEach(value, function (propertyValue, propertyKey) {

        /* Skipping function properties. */
        if (angular.isFunction(propertyValue)) {
          return;
        }

        result[propertyKey] = mapInterpolationParameters(propertyValue, iteratee, stack);
      });
      stack.splice(-1, 1); // remove last

      return result;
    } else if (angular.isNumber(value)) {
      return value;
    } else {
      return iteratee(value);
    }
  };
}

/**
 * @ngdoc object
 * @name pascalprecht.translate.$translateProvider
 * @description
 *
 * $translateProvider allows developers to register translation-tables, asynchronous loaders
 * and similar to configure translation behavior directly inside of a module.
 *
 */
angular.module('pascalprecht.translate')
.constant('pascalprechtTranslateOverrider', {})
.provider('$translate', $translate);

function $translate($STORAGE_KEY, $windowProvider, $translateSanitizationProvider, pascalprechtTranslateOverrider) {

  'use strict';

  var $translationTable = {},
      $preferredLanguage,
      $availableLanguageKeys = [],
      $languageKeyAliases,
      $fallbackLanguage,
      $fallbackWasString,
      $uses,
      $nextLang,
      $storageFactory,
      $storageKey = $STORAGE_KEY,
      $storagePrefix,
      $missingTranslationHandlerFactory,
      $interpolationFactory,
      $interpolatorFactories = [],
      $loaderFactory,
      $cloakClassName = 'translate-cloak',
      $loaderOptions,
      $notFoundIndicatorLeft,
      $notFoundIndicatorRight,
      $postCompilingEnabled = false,
      $forceAsyncReloadEnabled = false,
      $nestedObjectDelimeter = '.',
      $isReady = false,
      $keepContent = false,
      loaderCache,
      directivePriority = 0,
      statefulFilter = true,
      postProcessFn,
      uniformLanguageTagResolver = 'default',
      languageTagResolver = {
        'default': function (tag) {
          return (tag || '').split('-').join('_');
        },
        java: function (tag) {
          var temp = (tag || '').split('-').join('_');
          var parts = temp.split('_');
          return parts.length > 1 ? (parts[0].toLowerCase() + '_' + parts[1].toUpperCase()) : temp;
        },
        bcp47: function (tag) {
          var temp = (tag || '').split('_').join('-');
          var parts = temp.split('-');
          return parts.length > 1 ? (parts[0].toLowerCase() + '-' + parts[1].toUpperCase()) : temp;
        },
        'iso639-1': function (tag) {
          var temp = (tag || '').split('_').join('-');
          var parts = temp.split('-');
          return parts[0].toLowerCase();
        }
      };

  var version = '2.11.1';

  // tries to determine the browsers language
  var getFirstBrowserLanguage = function () {

    // internal purpose only
    if (angular.isFunction(pascalprechtTranslateOverrider.getLocale)) {
      return pascalprechtTranslateOverrider.getLocale();
    }

    var nav = $windowProvider.$get().navigator,
        browserLanguagePropertyKeys = ['language', 'browserLanguage', 'systemLanguage', 'userLanguage'],
        i,
        language;

    // support for HTML 5.1 "navigator.languages"
    if (angular.isArray(nav.languages)) {
      for (i = 0; i < nav.languages.length; i++) {
        language = nav.languages[i];
        if (language && language.length) {
          return language;
        }
      }
    }

    // support for other well known properties in browsers
    for (i = 0; i < browserLanguagePropertyKeys.length; i++) {
      language = nav[browserLanguagePropertyKeys[i]];
      if (language && language.length) {
        return language;
      }
    }

    return null;
  };
  getFirstBrowserLanguage.displayName = 'angular-translate/service: getFirstBrowserLanguage';

  // tries to determine the browsers locale
  var getLocale = function () {
    var locale = getFirstBrowserLanguage() || '';
    if (languageTagResolver[uniformLanguageTagResolver]) {
      locale = languageTagResolver[uniformLanguageTagResolver](locale);
    }
    return locale;
  };
  getLocale.displayName = 'angular-translate/service: getLocale';

  /**
   * @name indexOf
   * @private
   *
   * @description
   * indexOf polyfill. Kinda sorta.
   *
   * @param {array} array Array to search in.
   * @param {string} searchElement Element to search for.
   *
   * @returns {int} Index of search element.
   */
  var indexOf = function(array, searchElement) {
    for (var i = 0, len = array.length; i < len; i++) {
      if (array[i] === searchElement) {
        return i;
      }
    }
    return -1;
  };

  /**
   * @name trim
   * @private
   *
   * @description
   * trim polyfill
   *
   * @returns {string} The string stripped of whitespace from both ends
   */
  var trim = function() {
    return this.toString().replace(/^\s+|\s+$/g, '');
  };

  var negotiateLocale = function (preferred) {
    if(!preferred) {
      return;
    }

    var avail = [],
        locale = angular.lowercase(preferred),
        i = 0,
        n = $availableLanguageKeys.length;

    for (; i < n; i++) {
      avail.push(angular.lowercase($availableLanguageKeys[i]));
    }

    // Check for an exact match in our list of available keys
    if (indexOf(avail, locale) > -1) {
      return preferred;
    }

    if ($languageKeyAliases) {
      var alias;
      for (var langKeyAlias in $languageKeyAliases) {
        if ($languageKeyAliases.hasOwnProperty(langKeyAlias)) {
          var hasWildcardKey = false;
          var hasExactKey = Object.prototype.hasOwnProperty.call($languageKeyAliases, langKeyAlias) &&
            angular.lowercase(langKeyAlias) === angular.lowercase(preferred);

          if (langKeyAlias.slice(-1) === '*') {
            hasWildcardKey = langKeyAlias.slice(0, -1) === preferred.slice(0, langKeyAlias.length - 1);
          }
          if (hasExactKey || hasWildcardKey) {
            alias = $languageKeyAliases[langKeyAlias];
            if (indexOf(avail, angular.lowercase(alias)) > -1) {
              return alias;
            }
          }
        }
      }
    }

    // Check for a language code without region
    var parts = preferred.split('_');

    if (parts.length > 1 && indexOf(avail, angular.lowercase(parts[0])) > -1) {
      return parts[0];
    }

    // If everything fails, return undefined.
    return;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#translations
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Registers a new translation table for specific language key.
   *
   * To register a translation table for specific language, pass a defined language
   * key as first parameter.
   *
   * <pre>
   *  // register translation table for language: 'de_DE'
   *  $translateProvider.translations('de_DE', {
   *    'GREETING': 'Hallo Welt!'
   *  });
   *
   *  // register another one
   *  $translateProvider.translations('en_US', {
   *    'GREETING': 'Hello world!'
   *  });
   * </pre>
   *
   * When registering multiple translation tables for for the same language key,
   * the actual translation table gets extended. This allows you to define module
   * specific translation which only get added, once a specific module is loaded in
   * your app.
   *
   * Invoking this method with no arguments returns the translation table which was
   * registered with no language key. Invoking it with a language key returns the
   * related translation table.
   *
   * @param {string} langKey A language key.
   * @param {object} translationTable A plain old JavaScript object that represents a translation table.
   *
   */
  var translations = function (langKey, translationTable) {

    if (!langKey && !translationTable) {
      return $translationTable;
    }

    if (langKey && !translationTable) {
      if (angular.isString(langKey)) {
        return $translationTable[langKey];
      }
    } else {
      if (!angular.isObject($translationTable[langKey])) {
        $translationTable[langKey] = {};
      }
      angular.extend($translationTable[langKey], flatObject(translationTable));
    }
    return this;
  };

  this.translations = translations;

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#cloakClassName
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   *
   * Let's you change the class name for `translate-cloak` directive.
   * Default class name is `translate-cloak`.
   *
   * @param {string} name translate-cloak class name
   */
  this.cloakClassName = function (name) {
    if (!name) {
      return $cloakClassName;
    }
    $cloakClassName = name;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#nestedObjectDelimeter
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   *
   * Let's you change the delimiter for namespaced translations.
   * Default delimiter is `.`.
   *
   * @param {string} delimiter namespace separator
   */
  this.nestedObjectDelimeter = function (delimiter) {
    if (!delimiter) {
      return $nestedObjectDelimeter;
    }
    $nestedObjectDelimeter = delimiter;
    return this;
  };

  /**
   * @name flatObject
   * @private
   *
   * @description
   * Flats an object. This function is used to flatten given translation data with
   * namespaces, so they are later accessible via dot notation.
   */
  var flatObject = function (data, path, result, prevKey) {
    var key, keyWithPath, keyWithShortPath, val;

    if (!path) {
      path = [];
    }
    if (!result) {
      result = {};
    }
    for (key in data) {
      if (!Object.prototype.hasOwnProperty.call(data, key)) {
        continue;
      }
      val = data[key];
      if (angular.isObject(val)) {
        flatObject(val, path.concat(key), result, key);
      } else {
        keyWithPath = path.length ? ('' + path.join($nestedObjectDelimeter) + $nestedObjectDelimeter + key) : key;
        if(path.length && key === prevKey){
          // Create shortcut path (foo.bar == foo.bar.bar)
          keyWithShortPath = '' + path.join($nestedObjectDelimeter);
          // Link it to original path
          result[keyWithShortPath] = '@:' + keyWithPath;
        }
        result[keyWithPath] = val;
      }
    }
    return result;
  };
  flatObject.displayName = 'flatObject';

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#addInterpolation
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Adds interpolation services to angular-translate, so it can manage them.
   *
   * @param {object} factory Interpolation service factory
   */
  this.addInterpolation = function (factory) {
    $interpolatorFactories.push(factory);
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useMessageFormatInterpolation
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use interpolation functionality of messageformat.js.
   * This is useful when having high level pluralization and gender selection.
   */
  this.useMessageFormatInterpolation = function () {
    return this.useInterpolation('$translateMessageFormatInterpolation');
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useInterpolation
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate which interpolation style to use as default, application-wide.
   * Simply pass a factory/service name. The interpolation service has to implement
   * the correct interface.
   *
   * @param {string} factory Interpolation service name.
   */
  this.useInterpolation = function (factory) {
    $interpolationFactory = factory;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useSanitizeStrategy
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Simply sets a sanitation strategy type.
   *
   * @param {string} value Strategy type.
   */
  this.useSanitizeValueStrategy = function (value) {
    $translateSanitizationProvider.useStrategy(value);
    return this;
  };

 /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#preferredLanguage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells the module which of the registered translation tables to use for translation
   * at initial startup by passing a language key. Similar to `$translateProvider#use`
   * only that it says which language to **prefer**.
   *
   * @param {string} langKey A language key.
   */
  this.preferredLanguage = function(langKey) {
    if (langKey) {
      setupPreferredLanguage(langKey);
      return this;
    }
    return $preferredLanguage;
  };
  var setupPreferredLanguage = function (langKey) {
    if (langKey) {
      $preferredLanguage = langKey;
    }
    return $preferredLanguage;
  };
  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#translationNotFoundIndicator
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Sets an indicator which is used when a translation isn't found. E.g. when
   * setting the indicator as 'X' and one tries to translate a translation id
   * called `NOT_FOUND`, this will result in `X NOT_FOUND X`.
   *
   * Internally this methods sets a left indicator and a right indicator using
   * `$translateProvider.translationNotFoundIndicatorLeft()` and
   * `$translateProvider.translationNotFoundIndicatorRight()`.
   *
   * **Note**: These methods automatically add a whitespace between the indicators
   * and the translation id.
   *
   * @param {string} indicator An indicator, could be any string.
   */
  this.translationNotFoundIndicator = function (indicator) {
    this.translationNotFoundIndicatorLeft(indicator);
    this.translationNotFoundIndicatorRight(indicator);
    return this;
  };

  /**
   * ngdoc function
   * @name pascalprecht.translate.$translateProvider#translationNotFoundIndicatorLeft
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Sets an indicator which is used when a translation isn't found left to the
   * translation id.
   *
   * @param {string} indicator An indicator.
   */
  this.translationNotFoundIndicatorLeft = function (indicator) {
    if (!indicator) {
      return $notFoundIndicatorLeft;
    }
    $notFoundIndicatorLeft = indicator;
    return this;
  };

  /**
   * ngdoc function
   * @name pascalprecht.translate.$translateProvider#translationNotFoundIndicatorLeft
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Sets an indicator which is used when a translation isn't found right to the
   * translation id.
   *
   * @param {string} indicator An indicator.
   */
  this.translationNotFoundIndicatorRight = function (indicator) {
    if (!indicator) {
      return $notFoundIndicatorRight;
    }
    $notFoundIndicatorRight = indicator;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#fallbackLanguage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells the module which of the registered translation tables to use when missing translations
   * at initial startup by passing a language key. Similar to `$translateProvider#use`
   * only that it says which language to **fallback**.
   *
   * @param {string||array} langKey A language key.
   *
   */
  this.fallbackLanguage = function (langKey) {
    fallbackStack(langKey);
    return this;
  };

  var fallbackStack = function (langKey) {
    if (langKey) {
      if (angular.isString(langKey)) {
        $fallbackWasString = true;
        $fallbackLanguage = [ langKey ];
      } else if (angular.isArray(langKey)) {
        $fallbackWasString = false;
        $fallbackLanguage = langKey;
      }
      if (angular.isString($preferredLanguage)  && indexOf($fallbackLanguage, $preferredLanguage) < 0) {
        $fallbackLanguage.push($preferredLanguage);
      }

      return this;
    } else {
      if ($fallbackWasString) {
        return $fallbackLanguage[0];
      } else {
        return $fallbackLanguage;
      }
    }
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#use
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Set which translation table to use for translation by given language key. When
   * trying to 'use' a language which isn't provided, it'll throw an error.
   *
   * You actually don't have to use this method since `$translateProvider#preferredLanguage`
   * does the job too.
   *
   * @param {string} langKey A language key.
   */
  this.use = function (langKey) {
    if (langKey) {
      if (!$translationTable[langKey] && (!$loaderFactory)) {
        // only throw an error, when not loading translation data asynchronously
        throw new Error('$translateProvider couldn\'t find translationTable for langKey: \'' + langKey + '\'');
      }
      $uses = langKey;
      return this;
    }
    return $uses;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#resolveClientLocale
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * This returns the current browser/client's language key. The result is processed with the configured uniform tag resolver.
   *
   * @returns {string} the current client/browser language key
   */
  this.resolveClientLocale = function () {
    return getLocale();
  };

 /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#storageKey
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells the module which key must represent the choosed language by a user in the storage.
   *
   * @param {string} key A key for the storage.
   */
  var storageKey = function(key) {
    if (!key) {
      if ($storagePrefix) {
        return $storagePrefix + $storageKey;
      }
      return $storageKey;
    }
    $storageKey = key;
    return this;
  };

  this.storageKey = storageKey;

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useUrlLoader
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use `$translateUrlLoader` extension service as loader.
   *
   * @param {string} url Url
   * @param {Object=} options Optional configuration object
   */
  this.useUrlLoader = function (url, options) {
    return this.useLoader('$translateUrlLoader', angular.extend({ url: url }, options));
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useStaticFilesLoader
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use `$translateStaticFilesLoader` extension service as loader.
   *
   * @param {Object=} options Optional configuration object
   */
  this.useStaticFilesLoader = function (options) {
    return this.useLoader('$translateStaticFilesLoader', options);
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useLoader
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use any other service as loader.
   *
   * @param {string} loaderFactory Factory name to use
   * @param {Object=} options Optional configuration object
   */
  this.useLoader = function (loaderFactory, options) {
    $loaderFactory = loaderFactory;
    $loaderOptions = options || {};
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useLocalStorage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use `$translateLocalStorage` service as storage layer.
   *
   */
  this.useLocalStorage = function () {
    return this.useStorage('$translateLocalStorage');
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useCookieStorage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use `$translateCookieStorage` service as storage layer.
   */
  this.useCookieStorage = function () {
    return this.useStorage('$translateCookieStorage');
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useStorage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use custom service as storage layer.
   */
  this.useStorage = function (storageFactory) {
    $storageFactory = storageFactory;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#storagePrefix
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Sets prefix for storage key.
   *
   * @param {string} prefix Storage key prefix
   */
  this.storagePrefix = function (prefix) {
    if (!prefix) {
      return prefix;
    }
    $storagePrefix = prefix;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useMissingTranslationHandlerLog
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use built-in log handler when trying to translate
   * a translation Id which doesn't exist.
   *
   * This is actually a shortcut method for `useMissingTranslationHandler()`.
   *
   */
  this.useMissingTranslationHandlerLog = function () {
    return this.useMissingTranslationHandler('$translateMissingTranslationHandlerLog');
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useMissingTranslationHandler
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Expects a factory name which later gets instantiated with `$injector`.
   * This method can be used to tell angular-translate to use a custom
   * missingTranslationHandler. Just build a factory which returns a function
   * and expects a translation id as argument.
   *
   * Example:
   * <pre>
   *  app.config(function ($translateProvider) {
   *    $translateProvider.useMissingTranslationHandler('customHandler');
   *  });
   *
   *  app.factory('customHandler', function (dep1, dep2) {
   *    return function (translationId) {
   *      // something with translationId and dep1 and dep2
   *    };
   *  });
   * </pre>
   *
   * @param {string} factory Factory name
   */
  this.useMissingTranslationHandler = function (factory) {
    $missingTranslationHandlerFactory = factory;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#usePostCompiling
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * If post compiling is enabled, all translated values will be processed
   * again with AngularJS' $compile.
   *
   * Example:
   * <pre>
   *  app.config(function ($translateProvider) {
   *    $translateProvider.usePostCompiling(true);
   *  });
   * </pre>
   *
   * @param {string} factory Factory name
   */
  this.usePostCompiling = function (value) {
    $postCompilingEnabled = !(!value);
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#forceAsyncReload
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * If force async reload is enabled, async loader will always be called
   * even if $translationTable already contains the language key, adding
   * possible new entries to the $translationTable.
   *
   * Example:
   * <pre>
   *  app.config(function ($translateProvider) {
   *    $translateProvider.forceAsyncReload(true);
   *  });
   * </pre>
   *
   * @param {boolean} value - valid values are true or false
   */
  this.forceAsyncReload = function (value) {
    $forceAsyncReloadEnabled = !(!value);
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#uniformLanguageTag
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate which language tag should be used as a result when determining
   * the current browser language.
   *
   * This setting must be set before invoking {@link pascalprecht.translate.$translateProvider#methods_determinePreferredLanguage determinePreferredLanguage()}.
   *
   * <pre>
   * $translateProvider
   *   .uniformLanguageTag('bcp47')
   *   .determinePreferredLanguage()
   * </pre>
   *
   * The resolver currently supports:
   * * default
   *     (traditionally: hyphens will be converted into underscores, i.e. en-US => en_US)
   *     en-US => en_US
   *     en_US => en_US
   *     en-us => en_us
   * * java
   *     like default, but the second part will be always in uppercase
   *     en-US => en_US
   *     en_US => en_US
   *     en-us => en_US
   * * BCP 47 (RFC 4646 & 4647)
   *     en-US => en-US
   *     en_US => en-US
   *     en-us => en-US
   *
   * See also:
   * * http://en.wikipedia.org/wiki/IETF_language_tag
   * * http://www.w3.org/International/core/langtags/
   * * http://tools.ietf.org/html/bcp47
   *
   * @param {string|object} options - options (or standard)
   * @param {string} options.standard - valid values are 'default', 'bcp47', 'java'
   */
  this.uniformLanguageTag = function (options) {

    if (!options) {
      options = {};
    } else if (angular.isString(options)) {
      options = {
        standard: options
      };
    }

    uniformLanguageTagResolver = options.standard;

    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#determinePreferredLanguage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to try to determine on its own which language key
   * to set as preferred language. When `fn` is given, angular-translate uses it
   * to determine a language key, otherwise it uses the built-in `getLocale()`
   * method.
   *
   * The `getLocale()` returns a language key in the format `[lang]_[country]` or
   * `[lang]` depending on what the browser provides.
   *
   * Use this method at your own risk, since not all browsers return a valid
   * locale (see {@link pascalprecht.translate.$translateProvider#methods_uniformLanguageTag uniformLanguageTag()}).
   *
   * @param {Function=} fn Function to determine a browser's locale
   */
  this.determinePreferredLanguage = function (fn) {

    var locale = (fn && angular.isFunction(fn)) ? fn() : getLocale();

    if (!$availableLanguageKeys.length) {
      $preferredLanguage = locale;
    } else {
      $preferredLanguage = negotiateLocale(locale) || locale;
    }

    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#registerAvailableLanguageKeys
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Registers a set of language keys the app will work with. Use this method in
   * combination with
   * {@link pascalprecht.translate.$translateProvider#determinePreferredLanguage determinePreferredLanguage}.
   * When available languages keys are registered, angular-translate
   * tries to find the best fitting language key depending on the browsers locale,
   * considering your language key convention.
   *
   * @param {object} languageKeys Array of language keys the your app will use
   * @param {object=} aliases Alias map.
   */
  this.registerAvailableLanguageKeys = function (languageKeys, aliases) {
    if (languageKeys) {
      $availableLanguageKeys = languageKeys;
      if (aliases) {
        $languageKeyAliases = aliases;
      }
      return this;
    }
    return $availableLanguageKeys;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useLoaderCache
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Registers a cache for internal $http based loaders.
   * {@link pascalprecht.translate.$translationCache $translationCache}.
   * When false the cache will be disabled (default). When true or undefined
   * the cache will be a default (see $cacheFactory). When an object it will
   * be treat as a cache object itself: the usage is $http({cache: cache})
   *
   * @param {object} cache boolean, string or cache-object
   */
  this.useLoaderCache = function (cache) {
    if (cache === false) {
      // disable cache
      loaderCache = undefined;
    } else if (cache === true) {
      // enable cache using AJS defaults
      loaderCache = true;
    } else if (typeof(cache) === 'undefined') {
      // enable cache using default
      loaderCache = '$translationCache';
    } else if (cache) {
      // enable cache using given one (see $cacheFactory)
      loaderCache = cache;
    }
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#directivePriority
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Sets the default priority of the translate directive. The standard value is `0`.
   * Calling this function without an argument will return the current value.
   *
   * @param {number} priority for the translate-directive
   */
  this.directivePriority = function (priority) {
    if (priority === undefined) {
      // getter
      return directivePriority;
    } else {
      // setter with chaining
      directivePriority = priority;
      return this;
    }
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#statefulFilter
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Since AngularJS 1.3, filters which are not stateless (depending at the scope)
   * have to explicit define this behavior.
   * Sets whether the translate filter should be stateful or stateless. The standard value is `true`
   * meaning being stateful.
   * Calling this function without an argument will return the current value.
   *
   * @param {boolean} state - defines the state of the filter
   */
  this.statefulFilter = function (state) {
    if (state === undefined) {
      // getter
      return statefulFilter;
    } else {
      // setter with chaining
      statefulFilter = state;
      return this;
    }
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#postProcess
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * The post processor will be intercept right after the translation result. It can modify the result.
   *
   * @param {object} fn Function or service name (string) to be called after the translation value has been set / resolved. The function itself will enrich every value being processed and then continue the normal resolver process
   */
  this.postProcess = function (fn) {
    if (fn) {
      postProcessFn = fn;
    } else {
      postProcessFn = undefined;
    }
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#keepContent
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * If keepContent is set to true than translate directive will always use innerHTML
   * as a default translation
   *
   * Example:
   * <pre>
   *  app.config(function ($translateProvider) {
   *    $translateProvider.keepContent(true);
   *  });
   * </pre>
   *
   * @param {boolean} value - valid values are true or false
   */
  this.keepContent = function (value) {
    $keepContent = !(!value);
    return this;
  };

  /**
   * @ngdoc object
   * @name pascalprecht.translate.$translate
   * @requires $interpolate
   * @requires $log
   * @requires $rootScope
   * @requires $q
   *
   * @description
   * The `$translate` service is the actual core of angular-translate. It expects a translation id
   * and optional interpolate parameters to translate contents.
   *
   * <pre>
   *  $translate('HEADLINE_TEXT').then(function (translation) {
   *    $scope.translatedText = translation;
   *  });
   * </pre>
   *
   * @param {string|array} translationId A token which represents a translation id
   *                                     This can be optionally an array of translation ids which
   *                                     results that the function returns an object where each key
   *                                     is the translation id and the value the translation.
   * @param {object=} interpolateParams An object hash for dynamic values
   * @param {string} interpolationId The id of the interpolation to use
   * @param {string} defaultTranslationText the optional default translation text that is written as
   *                                        as default text in case it is not found in any configured language
   * @param {string} forceLanguage A language to be used instead of the current language
   * @returns {object} promise
   */
  this.$get = [
    '$log',
    '$injector',
    '$rootScope',
    '$q',
    function ($log, $injector, $rootScope, $q) {

      var Storage,
          defaultInterpolator = $injector.get($interpolationFactory || '$translateDefaultInterpolation'),
          pendingLoader = false,
          interpolatorHashMap = {},
          langPromises = {},
          fallbackIndex,
          startFallbackIteration;

      var $translate = function (translationId, interpolateParams, interpolationId, defaultTranslationText, forceLanguage) {
        if (!$uses && $preferredLanguage) {
          $uses = $preferredLanguage;
        }
        var uses = (forceLanguage && forceLanguage !== $uses) ? // we don't want to re-negotiate $uses
              (negotiateLocale(forceLanguage) || forceLanguage) : $uses;

        // Check forceLanguage is present
        if (forceLanguage) {
          loadTranslationsIfMissing(forceLanguage);
        }

        // Duck detection: If the first argument is an array, a bunch of translations was requested.
        // The result is an object.
        if (angular.isArray(translationId)) {
          // Inspired by Q.allSettled by Kris Kowal
          // https://github.com/kriskowal/q/blob/b0fa72980717dc202ffc3cbf03b936e10ebbb9d7/q.js#L1553-1563
          // This transforms all promises regardless resolved or rejected
          var translateAll = function (translationIds) {
            var results = {}; // storing the actual results
            var promises = []; // promises to wait for
            // Wraps the promise a) being always resolved and b) storing the link id->value
            var translate = function (translationId) {
              var deferred = $q.defer();
              var regardless = function (value) {
                results[translationId] = value;
                deferred.resolve([translationId, value]);
              };
              // we don't care whether the promise was resolved or rejected; just store the values
              $translate(translationId, interpolateParams, interpolationId, defaultTranslationText, forceLanguage).then(regardless, regardless);
              return deferred.promise;
            };
            for (var i = 0, c = translationIds.length; i < c; i++) {
              promises.push(translate(translationIds[i]));
            }
            // wait for all (including storing to results)
            return $q.all(promises).then(function () {
              // return the results
              return results;
            });
          };
          return translateAll(translationId);
        }

        var deferred = $q.defer();

        // trim off any whitespace
        if (translationId) {
          translationId = trim.apply(translationId);
        }

        var promiseToWaitFor = (function () {
          var promise = $preferredLanguage ?
            langPromises[$preferredLanguage] :
            langPromises[uses];

          fallbackIndex = 0;

          if ($storageFactory && !promise) {
            // looks like there's no pending promise for $preferredLanguage or
            // $uses. Maybe there's one pending for a language that comes from
            // storage.
            var langKey = Storage.get($storageKey);
            promise = langPromises[langKey];

            if ($fallbackLanguage && $fallbackLanguage.length) {
                var index = indexOf($fallbackLanguage, langKey);
                // maybe the language from storage is also defined as fallback language
                // we increase the fallback language index to not search in that language
                // as fallback, since it's probably the first used language
                // in that case the index starts after the first element
                fallbackIndex = (index === 0) ? 1 : 0;

                // but we can make sure to ALWAYS fallback to preferred language at least
                if (indexOf($fallbackLanguage, $preferredLanguage) < 0) {
                  $fallbackLanguage.push($preferredLanguage);
                }
            }
          }
          return promise;
        }());

        if (!promiseToWaitFor) {
          // no promise to wait for? okay. Then there's no loader registered
          // nor is a one pending for language that comes from storage.
          // We can just translate.
          determineTranslation(translationId, interpolateParams, interpolationId, defaultTranslationText, uses).then(deferred.resolve, deferred.reject);
        } else {
          var promiseResolved = function () {
            // $uses may have changed while waiting
            if (!forceLanguage) {
              uses = $uses;
            }
            determineTranslation(translationId, interpolateParams, interpolationId, defaultTranslationText, uses).then(deferred.resolve, deferred.reject);
          };
          promiseResolved.displayName = 'promiseResolved';

          promiseToWaitFor['finally'](promiseResolved);
        }
        return deferred.promise;
      };

      /**
       * @name applyNotFoundIndicators
       * @private
       *
       * @description
       * Applies not fount indicators to given translation id, if needed.
       * This function gets only executed, if a translation id doesn't exist,
       * which is why a translation id is expected as argument.
       *
       * @param {string} translationId Translation id.
       * @returns {string} Same as given translation id but applied with not found
       * indicators.
       */
      var applyNotFoundIndicators = function (translationId) {
        // applying notFoundIndicators
        if ($notFoundIndicatorLeft) {
          translationId = [$notFoundIndicatorLeft, translationId].join(' ');
        }
        if ($notFoundIndicatorRight) {
          translationId = [translationId, $notFoundIndicatorRight].join(' ');
        }
        return translationId;
      };

      /**
       * @name useLanguage
       * @private
       *
       * @description
       * Makes actual use of a language by setting a given language key as used
       * language and informs registered interpolators to also use the given
       * key as locale.
       *
       * @param {string} key Locale key.
       */
      var useLanguage = function (key) {
        $uses = key;

        // make sure to store new language key before triggering success event
        if ($storageFactory) {
          Storage.put($translate.storageKey(), $uses);
        }

        $rootScope.$emit('$translateChangeSuccess', {language: key});

        // inform default interpolator
        defaultInterpolator.setLocale($uses);

        var eachInterpolator = function (interpolator, id) {
          interpolatorHashMap[id].setLocale($uses);
        };
        eachInterpolator.displayName = 'eachInterpolatorLocaleSetter';

        // inform all others too!
        angular.forEach(interpolatorHashMap, eachInterpolator);
        $rootScope.$emit('$translateChangeEnd', {language: key});
      };

      /**
       * @name loadAsync
       * @private
       *
       * @description
       * Kicks of registered async loader using `$injector` and applies existing
       * loader options. When resolved, it updates translation tables accordingly
       * or rejects with given language key.
       *
       * @param {string} key Language key.
       * @return {Promise} A promise.
       */
      var loadAsync = function (key) {
        if (!key) {
          throw 'No language key specified for loading.';
        }

        var deferred = $q.defer();

        $rootScope.$emit('$translateLoadingStart', {language: key});
        pendingLoader = true;

        var cache = loaderCache;
        if (typeof(cache) === 'string') {
          // getting on-demand instance of loader
          cache = $injector.get(cache);
        }

        var loaderOptions = angular.extend({}, $loaderOptions, {
          key: key,
          $http: angular.extend({}, {
            cache: cache
          }, $loaderOptions.$http)
        });

        var onLoaderSuccess = function (data) {
          var translationTable = {};
          $rootScope.$emit('$translateLoadingSuccess', {language: key});

          if (angular.isArray(data)) {
            angular.forEach(data, function (table) {
              angular.extend(translationTable, flatObject(table));
            });
          } else {
            angular.extend(translationTable, flatObject(data));
          }
          pendingLoader = false;
          deferred.resolve({
            key: key,
            table: translationTable
          });
          $rootScope.$emit('$translateLoadingEnd', {language: key});
        };
        onLoaderSuccess.displayName = 'onLoaderSuccess';

        var onLoaderError = function (key) {
          $rootScope.$emit('$translateLoadingError', {language: key});
          deferred.reject(key);
          $rootScope.$emit('$translateLoadingEnd', {language: key});
        };
        onLoaderError.displayName = 'onLoaderError';

        $injector.get($loaderFactory)(loaderOptions)
          .then(onLoaderSuccess, onLoaderError);

        return deferred.promise;
      };

      if ($storageFactory) {
        Storage = $injector.get($storageFactory);

        if (!Storage.get || !Storage.put) {
          throw new Error('Couldn\'t use storage \'' + $storageFactory + '\', missing get() or put() method!');
        }
      }

      // if we have additional interpolations that were added via
      // $translateProvider.addInterpolation(), we have to map'em
      if ($interpolatorFactories.length) {
        var eachInterpolationFactory = function (interpolatorFactory) {
          var interpolator = $injector.get(interpolatorFactory);
          // setting initial locale for each interpolation service
          interpolator.setLocale($preferredLanguage || $uses);
          // make'em recognizable through id
          interpolatorHashMap[interpolator.getInterpolationIdentifier()] = interpolator;
        };
        eachInterpolationFactory.displayName = 'interpolationFactoryAdder';

        angular.forEach($interpolatorFactories, eachInterpolationFactory);
      }

      /**
       * @name getTranslationTable
       * @private
       *
       * @description
       * Returns a promise that resolves to the translation table
       * or is rejected if an error occurred.
       *
       * @param langKey
       * @returns {Q.promise}
       */
      var getTranslationTable = function (langKey) {
        var deferred = $q.defer();
        if (Object.prototype.hasOwnProperty.call($translationTable, langKey)) {
          deferred.resolve($translationTable[langKey]);
        } else if (langPromises[langKey]) {
          var onResolve = function (data) {
            translations(data.key, data.table);
            deferred.resolve(data.table);
          };
          onResolve.displayName = 'translationTableResolver';
          langPromises[langKey].then(onResolve, deferred.reject);
        } else {
          deferred.reject();
        }
        return deferred.promise;
      };

      /**
       * @name getFallbackTranslation
       * @private
       *
       * @description
       * Returns a promise that will resolve to the translation
       * or be rejected if no translation was found for the language.
       * This function is currently only used for fallback language translation.
       *
       * @param langKey The language to translate to.
       * @param translationId
       * @param interpolateParams
       * @param Interpolator
       * @returns {Q.promise}
       */
      var getFallbackTranslation = function (langKey, translationId, interpolateParams, Interpolator) {
        var deferred = $q.defer();

        var onResolve = function (translationTable) {
          if (Object.prototype.hasOwnProperty.call(translationTable, translationId)) {
            Interpolator.setLocale(langKey);
            var translation = translationTable[translationId];
            if (translation.substr(0, 2) === '@:') {
              getFallbackTranslation(langKey, translation.substr(2), interpolateParams, Interpolator)
                .then(deferred.resolve, deferred.reject);
            } else {
              var interpolatedValue = Interpolator.interpolate(translationTable[translationId], interpolateParams);
              interpolatedValue = applyPostProcessing(translationId, translationTable[translationId], interpolatedValue, interpolateParams, langKey);

              deferred.resolve(interpolatedValue);

            }
            Interpolator.setLocale($uses);
          } else {
            deferred.reject();
          }
        };
        onResolve.displayName = 'fallbackTranslationResolver';

        getTranslationTable(langKey).then(onResolve, deferred.reject);

        return deferred.promise;
      };

      /**
       * @name getFallbackTranslationInstant
       * @private
       *
       * @description
       * Returns a translation
       * This function is currently only used for fallback language translation.
       *
       * @param langKey The language to translate to.
       * @param translationId
       * @param interpolateParams
       * @param Interpolator
       * @returns {string} translation
       */
      var getFallbackTranslationInstant = function (langKey, translationId, interpolateParams, Interpolator) {
        var result, translationTable = $translationTable[langKey];

        if (translationTable && Object.prototype.hasOwnProperty.call(translationTable, translationId)) {
          Interpolator.setLocale(langKey);
          result = Interpolator.interpolate(translationTable[translationId], interpolateParams);
          result = applyPostProcessing(translationId, translationTable[translationId], result, interpolateParams, langKey);
          if (result.substr(0, 2) === '@:') {
            return getFallbackTranslationInstant(langKey, result.substr(2), interpolateParams, Interpolator);
          }
          Interpolator.setLocale($uses);
        }

        return result;
      };


      /**
       * @name translateByHandler
       * @private
       *
       * Translate by missing translation handler.
       *
       * @param translationId
       * @param interpolateParams
       * @param defaultTranslationText
       * @returns translation created by $missingTranslationHandler or translationId is $missingTranslationHandler is
       * absent
       */
      var translateByHandler = function (translationId, interpolateParams, defaultTranslationText) {
        // If we have a handler factory - we might also call it here to determine if it provides
        // a default text for a translationid that can't be found anywhere in our tables
        if ($missingTranslationHandlerFactory) {
          var resultString = $injector.get($missingTranslationHandlerFactory)(translationId, $uses, interpolateParams, defaultTranslationText);
          if (resultString !== undefined) {
            return resultString;
          } else {
            return translationId;
          }
        } else {
          return translationId;
        }
      };

      /**
       * @name resolveForFallbackLanguage
       * @private
       *
       * Recursive helper function for fallbackTranslation that will sequentially look
       * for a translation in the fallbackLanguages starting with fallbackLanguageIndex.
       *
       * @param fallbackLanguageIndex
       * @param translationId
       * @param interpolateParams
       * @param Interpolator
       * @returns {Q.promise} Promise that will resolve to the translation.
       */
      var resolveForFallbackLanguage = function (fallbackLanguageIndex, translationId, interpolateParams, Interpolator, defaultTranslationText) {
        var deferred = $q.defer();

        if (fallbackLanguageIndex < $fallbackLanguage.length) {
          var langKey = $fallbackLanguage[fallbackLanguageIndex];
          getFallbackTranslation(langKey, translationId, interpolateParams, Interpolator).then(
            function (data) {
                deferred.resolve(data);
            },
            function () {
              // Look in the next fallback language for a translation.
              // It delays the resolving by passing another promise to resolve.
              return resolveForFallbackLanguage(fallbackLanguageIndex + 1, translationId, interpolateParams, Interpolator, defaultTranslationText).then(deferred.resolve, deferred.reject);
            }
          );
        } else {
          // No translation found in any fallback language
          // if a default translation text is set in the directive, then return this as a result
          if (defaultTranslationText) {
            deferred.resolve(defaultTranslationText);
          } else {
            // if no default translation is set and an error handler is defined, send it to the handler
            // and then return the result
            if ($missingTranslationHandlerFactory) {
              deferred.resolve(translateByHandler(translationId, interpolateParams));
            } else {
              deferred.reject(translateByHandler(translationId, interpolateParams));
            }

          }
        }
        return deferred.promise;
      };

      /**
       * @name resolveForFallbackLanguageInstant
       * @private
       *
       * Recursive helper function for fallbackTranslation that will sequentially look
       * for a translation in the fallbackLanguages starting with fallbackLanguageIndex.
       *
       * @param fallbackLanguageIndex
       * @param translationId
       * @param interpolateParams
       * @param Interpolator
       * @returns {string} translation
       */
      var resolveForFallbackLanguageInstant = function (fallbackLanguageIndex, translationId, interpolateParams, Interpolator) {
        var result;

        if (fallbackLanguageIndex < $fallbackLanguage.length) {
          var langKey = $fallbackLanguage[fallbackLanguageIndex];
          result = getFallbackTranslationInstant(langKey, translationId, interpolateParams, Interpolator);
          if (!result) {
            result = resolveForFallbackLanguageInstant(fallbackLanguageIndex + 1, translationId, interpolateParams, Interpolator);
          }
        }
        return result;
      };

      /**
       * Translates with the usage of the fallback languages.
       *
       * @param translationId
       * @param interpolateParams
       * @param Interpolator
       * @returns {Q.promise} Promise, that resolves to the translation.
       */
      var fallbackTranslation = function (translationId, interpolateParams, Interpolator, defaultTranslationText) {
        // Start with the fallbackLanguage with index 0
        return resolveForFallbackLanguage((startFallbackIteration>0 ? startFallbackIteration : fallbackIndex), translationId, interpolateParams, Interpolator, defaultTranslationText);
      };

      /**
       * Translates with the usage of the fallback languages.
       *
       * @param translationId
       * @param interpolateParams
       * @param Interpolator
       * @returns {String} translation
       */
      var fallbackTranslationInstant = function (translationId, interpolateParams, Interpolator) {
        // Start with the fallbackLanguage with index 0
        return resolveForFallbackLanguageInstant((startFallbackIteration>0 ? startFallbackIteration : fallbackIndex), translationId, interpolateParams, Interpolator);
      };

      var determineTranslation = function (translationId, interpolateParams, interpolationId, defaultTranslationText, uses) {

        var deferred = $q.defer();

        var table = uses ? $translationTable[uses] : $translationTable,
            Interpolator = (interpolationId) ? interpolatorHashMap[interpolationId] : defaultInterpolator;

        // if the translation id exists, we can just interpolate it
        if (table && Object.prototype.hasOwnProperty.call(table, translationId)) {
          var translation = table[translationId];

          // If using link, rerun $translate with linked translationId and return it
          if (translation.substr(0, 2) === '@:') {

            $translate(translation.substr(2), interpolateParams, interpolationId, defaultTranslationText, uses)
              .then(deferred.resolve, deferred.reject);
          } else {
            //
            var resolvedTranslation = Interpolator.interpolate(translation, interpolateParams);
            resolvedTranslation = applyPostProcessing(translationId, translation, resolvedTranslation, interpolateParams, uses);
            deferred.resolve(resolvedTranslation);
          }
        } else {
          var missingTranslationHandlerTranslation;
          // for logging purposes only (as in $translateMissingTranslationHandlerLog), value is not returned to promise
          if ($missingTranslationHandlerFactory && !pendingLoader) {
            missingTranslationHandlerTranslation = translateByHandler(translationId, interpolateParams, defaultTranslationText);
          }

          // since we couldn't translate the inital requested translation id,
          // we try it now with one or more fallback languages, if fallback language(s) is
          // configured.
          if (uses && $fallbackLanguage && $fallbackLanguage.length) {
            fallbackTranslation(translationId, interpolateParams, Interpolator, defaultTranslationText)
                .then(function (translation) {
                  deferred.resolve(translation);
                }, function (_translationId) {
                  deferred.reject(applyNotFoundIndicators(_translationId));
                });
          } else if ($missingTranslationHandlerFactory && !pendingLoader && missingTranslationHandlerTranslation) {
            // looks like the requested translation id doesn't exists.
            // Now, if there is a registered handler for missing translations and no
            // asyncLoader is pending, we execute the handler
            if (defaultTranslationText) {
              deferred.resolve(defaultTranslationText);
              } else {
                deferred.resolve(missingTranslationHandlerTranslation);
              }
          } else {
            if (defaultTranslationText) {
              deferred.resolve(defaultTranslationText);
            } else {
              deferred.reject(applyNotFoundIndicators(translationId));
            }
          }
        }
        return deferred.promise;
      };

      var determineTranslationInstant = function (translationId, interpolateParams, interpolationId, uses) {

        var result, table = uses ? $translationTable[uses] : $translationTable,
            Interpolator = defaultInterpolator;

        // if the interpolation id exists use custom interpolator
        if (interpolatorHashMap && Object.prototype.hasOwnProperty.call(interpolatorHashMap, interpolationId)) {
          Interpolator = interpolatorHashMap[interpolationId];
        }

        // if the translation id exists, we can just interpolate it
        if (table && Object.prototype.hasOwnProperty.call(table, translationId)) {
          var translation = table[translationId];

          // If using link, rerun $translate with linked translationId and return it
          if (translation.substr(0, 2) === '@:') {
            result = determineTranslationInstant(translation.substr(2), interpolateParams, interpolationId, uses);
          } else {
            result = Interpolator.interpolate(translation, interpolateParams);
            result = applyPostProcessing(translationId, translation, result, interpolateParams, uses);
          }
        } else {
          var missingTranslationHandlerTranslation;
          // for logging purposes only (as in $translateMissingTranslationHandlerLog), value is not returned to promise
          if ($missingTranslationHandlerFactory && !pendingLoader) {
            missingTranslationHandlerTranslation = translateByHandler(translationId, interpolateParams);
          }

          // since we couldn't translate the inital requested translation id,
          // we try it now with one or more fallback languages, if fallback language(s) is
          // configured.
          if (uses && $fallbackLanguage && $fallbackLanguage.length) {
            fallbackIndex = 0;
            result = fallbackTranslationInstant(translationId, interpolateParams, Interpolator);
          } else if ($missingTranslationHandlerFactory && !pendingLoader && missingTranslationHandlerTranslation) {
            // looks like the requested translation id doesn't exists.
            // Now, if there is a registered handler for missing translations and no
            // asyncLoader is pending, we execute the handler
            result = missingTranslationHandlerTranslation;
          } else {
            result = applyNotFoundIndicators(translationId);
          }
        }

        return result;
      };

      var clearNextLangAndPromise = function(key) {
        if ($nextLang === key) {
          $nextLang = undefined;
        }
        langPromises[key] = undefined;
      };

      var applyPostProcessing = function (translationId, translation, resolvedTranslation, interpolateParams, uses) {
        var fn = postProcessFn;

        if (fn) {

          if (typeof(fn) === 'string') {
            // getting on-demand instance
            fn = $injector.get(fn);
          }
          if (fn) {
            return fn(translationId, translation, resolvedTranslation, interpolateParams, uses);
          }
        }

        return resolvedTranslation;
      };

      var loadTranslationsIfMissing = function (key) {
        if (!$translationTable[key] && $loaderFactory && !langPromises[key]) {
          langPromises[key] = loadAsync(key).then(function (translation) {
            translations(translation.key, translation.table);
            return translation;
          });
        }
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#preferredLanguage
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns the language key for the preferred language.
       *
       * @param {string} langKey language String or Array to be used as preferredLanguage (changing at runtime)
       *
       * @return {string} preferred language key
       */
      $translate.preferredLanguage = function (langKey) {
        if(langKey) {
          setupPreferredLanguage(langKey);
        }
        return $preferredLanguage;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#cloakClassName
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns the configured class name for `translate-cloak` directive.
       *
       * @return {string} cloakClassName
       */
      $translate.cloakClassName = function () {
        return $cloakClassName;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#nestedObjectDelimeter
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns the configured delimiter for nested namespaces.
       *
       * @return {string} nestedObjectDelimeter
       */
      $translate.nestedObjectDelimeter = function () {
        return $nestedObjectDelimeter;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#fallbackLanguage
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns the language key for the fallback languages or sets a new fallback stack.
       *
       * @param {string=} langKey language String or Array of fallback languages to be used (to change stack at runtime)
       *
       * @return {string||array} fallback language key
       */
      $translate.fallbackLanguage = function (langKey) {
        if (langKey !== undefined && langKey !== null) {
          fallbackStack(langKey);

          // as we might have an async loader initiated and a new translation language might have been defined
          // we need to add the promise to the stack also. So - iterate.
          if ($loaderFactory) {
            if ($fallbackLanguage && $fallbackLanguage.length) {
              for (var i = 0, len = $fallbackLanguage.length; i < len; i++) {
                if (!langPromises[$fallbackLanguage[i]]) {
                  langPromises[$fallbackLanguage[i]] = loadAsync($fallbackLanguage[i]);
                }
              }
            }
          }
          $translate.use($translate.use());
        }
        if ($fallbackWasString) {
          return $fallbackLanguage[0];
        } else {
          return $fallbackLanguage;
        }

      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#useFallbackLanguage
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Sets the first key of the fallback language stack to be used for translation.
       * Therefore all languages in the fallback array BEFORE this key will be skipped!
       *
       * @param {string=} langKey Contains the langKey the iteration shall start with. Set to false if you want to
       * get back to the whole stack
       */
      $translate.useFallbackLanguage = function (langKey) {
        if (langKey !== undefined && langKey !== null) {
          if (!langKey) {
            startFallbackIteration = 0;
          } else {
            var langKeyPosition = indexOf($fallbackLanguage, langKey);
            if (langKeyPosition > -1) {
              startFallbackIteration = langKeyPosition;
            }
          }

        }

      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#proposedLanguage
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns the language key of language that is currently loaded asynchronously.
       *
       * @return {string} language key
       */
      $translate.proposedLanguage = function () {
        return $nextLang;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#storage
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns registered storage.
       *
       * @return {object} Storage
       */
      $translate.storage = function () {
        return Storage;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#negotiateLocale
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns a language key based on available languages and language aliases. If a
       * language key cannot be resolved, returns undefined.
       *
       * If no or a falsy key is given, returns undefined.
       *
       * @param {string} [key] Language key
       * @return {string|undefined} Language key or undefined if no language key is found.
       */
      $translate.negotiateLocale = negotiateLocale;

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#use
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Tells angular-translate which language to use by given language key. This method is
       * used to change language at runtime. It also takes care of storing the language
       * key in a configured store to let your app remember the choosed language.
       *
       * When trying to 'use' a language which isn't available it tries to load it
       * asynchronously with registered loaders.
       *
       * Returns promise object with loaded language file data or string of the currently used language.
       *
       * If no or a falsy key is given it returns the currently used language key.
       * The returned string will be ```undefined``` if setting up $translate hasn't finished.
       * @example
       * $translate.use("en_US").then(function(data){
       *   $scope.text = $translate("HELLO");
       * });
       *
       * @param {string} [key] Language key
       * @return {object|string} Promise with loaded language data or the language key if a falsy param was given.
       */
      $translate.use = function (key) {
        if (!key) {
          return $uses;
        }

        var deferred = $q.defer();

        $rootScope.$emit('$translateChangeStart', {language: key});

        // Try to get the aliased language key
        var aliasedKey = negotiateLocale(key);
        // Ensure only registered language keys will be loaded
        if ($availableLanguageKeys.length > 0 && !aliasedKey) {
          return $q.reject(key);
        }

        if (aliasedKey) {
          key = aliasedKey;
        }

        // if there isn't a translation table for the language we've requested,
        // we load it asynchronously
        $nextLang = key;
        if (($forceAsyncReloadEnabled || !$translationTable[key]) && $loaderFactory && !langPromises[key]) {
          langPromises[key] = loadAsync(key).then(function (translation) {
            translations(translation.key, translation.table);
            deferred.resolve(translation.key);
            if ($nextLang === key) {
              useLanguage(translation.key);
            }
            return translation;
          }, function (key) {
            $rootScope.$emit('$translateChangeError', {language: key});
            deferred.reject(key);
            $rootScope.$emit('$translateChangeEnd', {language: key});
            return $q.reject(key);
          });
          langPromises[key]['finally'](function () {
            clearNextLangAndPromise(key);
          });
        } else if (langPromises[key]) {
          // we are already loading this asynchronously
          // resolve our new deferred when the old langPromise is resolved
          langPromises[key].then(function (translation) {
            if ($nextLang === translation.key) {
              useLanguage(translation.key);
            }
            deferred.resolve(translation.key);
            return translation;
          }, function (key) {
            // find first available fallback language if that request has failed
            if (!$uses && $fallbackLanguage && $fallbackLanguage.length > 0) {
              return $translate.use($fallbackLanguage[0]).then(deferred.resolve, deferred.reject);
            } else {
              return deferred.reject(key);
            }
          });
        } else {
          deferred.resolve(key);
          useLanguage(key);
        }

        return deferred.promise;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#resolveClientLocale
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * This returns the current browser/client's language key. The result is processed with the configured uniform tag resolver.
       *
       * @returns {string} the current client/browser language key
       */
      $translate.resolveClientLocale = function () {
        return getLocale();
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#storageKey
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns the key for the storage.
       *
       * @return {string} storage key
       */
      $translate.storageKey = function () {
        return storageKey();
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#isPostCompilingEnabled
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns whether post compiling is enabled or not
       *
       * @return {bool} storage key
       */
      $translate.isPostCompilingEnabled = function () {
        return $postCompilingEnabled;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#isForceAsyncReloadEnabled
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns whether force async reload is enabled or not
       *
       * @return {boolean} forceAsyncReload value
       */
      $translate.isForceAsyncReloadEnabled = function () {
        return $forceAsyncReloadEnabled;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#isKeepContent
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns whether keepContent or not
       *
       * @return {boolean} keepContent value
       */
      $translate.isKeepContent = function () {
        return $keepContent;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#refresh
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Refreshes a translation table pointed by the given langKey. If langKey is not specified,
       * the module will drop all existent translation tables and load new version of those which
       * are currently in use.
       *
       * Refresh means that the module will drop target translation table and try to load it again.
       *
       * In case there are no loaders registered the refresh() method will throw an Error.
       *
       * If the module is able to refresh translation tables refresh() method will broadcast
       * $translateRefreshStart and $translateRefreshEnd events.
       *
       * @example
       * // this will drop all currently existent translation tables and reload those which are
       * // currently in use
       * $translate.refresh();
       * // this will refresh a translation table for the en_US language
       * $translate.refresh('en_US');
       *
       * @param {string} langKey A language key of the table, which has to be refreshed
       *
       * @return {promise} Promise, which will be resolved in case a translation tables refreshing
       * process is finished successfully, and reject if not.
       */
      $translate.refresh = function (langKey) {
        if (!$loaderFactory) {
          throw new Error('Couldn\'t refresh translation table, no loader registered!');
        }

        var deferred = $q.defer();

        function resolve() {
          deferred.resolve();
          $rootScope.$emit('$translateRefreshEnd', {language: langKey});
        }

        function reject() {
          deferred.reject();
          $rootScope.$emit('$translateRefreshEnd', {language: langKey});
        }

        $rootScope.$emit('$translateRefreshStart', {language: langKey});

        if (!langKey) {
          // if there's no language key specified we refresh ALL THE THINGS!
          var tables = [], loadingKeys = {};

          // reload registered fallback languages
          if ($fallbackLanguage && $fallbackLanguage.length) {
            for (var i = 0, len = $fallbackLanguage.length; i < len; i++) {
              tables.push(loadAsync($fallbackLanguage[i]));
              loadingKeys[$fallbackLanguage[i]] = true;
            }
          }

          // reload currently used language
          if ($uses && !loadingKeys[$uses]) {
            tables.push(loadAsync($uses));
          }

          var allTranslationsLoaded = function (tableData) {
            $translationTable = {};
            angular.forEach(tableData, function (data) {
              translations(data.key, data.table);
            });
            if ($uses) {
              useLanguage($uses);
            }
            resolve();
          };
          allTranslationsLoaded.displayName = 'refreshPostProcessor';

          $q.all(tables).then(allTranslationsLoaded, reject);

        } else if ($translationTable[langKey]) {

          var oneTranslationsLoaded = function (data) {
            translations(data.key, data.table);
            if (langKey === $uses) {
              useLanguage($uses);
            }
            resolve();
            return data;
          };
          oneTranslationsLoaded.displayName = 'refreshPostProcessor';

          loadAsync(langKey).then(oneTranslationsLoaded, reject);

        } else {
          reject();
        }
        return deferred.promise;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#instant
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns a translation instantly from the internal state of loaded translation. All rules
       * regarding the current language, the preferred language of even fallback languages will be
       * used except any promise handling. If a language was not found, an asynchronous loading
       * will be invoked in the background.
       *
       * @param {string|array} translationId A token which represents a translation id
       *                                     This can be optionally an array of translation ids which
       *                                     results that the function's promise returns an object where
       *                                     each key is the translation id and the value the translation.
       * @param {object} interpolateParams Params
       * @param {string} interpolationId The id of the interpolation to use
       * @param {string} forceLanguage A language to be used instead of the current language
       *
       * @return {string|object} translation
       */
      $translate.instant = function (translationId, interpolateParams, interpolationId, forceLanguage) {

        // we don't want to re-negotiate $uses
        var uses = (forceLanguage && forceLanguage !== $uses) ? // we don't want to re-negotiate $uses
              (negotiateLocale(forceLanguage) || forceLanguage) : $uses;

        // Detect undefined and null values to shorten the execution and prevent exceptions
        if (translationId === null || angular.isUndefined(translationId)) {
          return translationId;
        }

        // Check forceLanguage is present
        if (forceLanguage) {
          loadTranslationsIfMissing(forceLanguage);
        }

        // Duck detection: If the first argument is an array, a bunch of translations was requested.
        // The result is an object.
        if (angular.isArray(translationId)) {
          var results = {};
          for (var i = 0, c = translationId.length; i < c; i++) {
            results[translationId[i]] = $translate.instant(translationId[i], interpolateParams, interpolationId, forceLanguage);
          }
          return results;
        }

        // We discarded unacceptable values. So we just need to verify if translationId is empty String
        if (angular.isString(translationId) && translationId.length < 1) {
          return translationId;
        }

        // trim off any whitespace
        if (translationId) {
          translationId = trim.apply(translationId);
        }

        var result, possibleLangKeys = [];
        if ($preferredLanguage) {
          possibleLangKeys.push($preferredLanguage);
        }
        if (uses) {
          possibleLangKeys.push(uses);
        }
        if ($fallbackLanguage && $fallbackLanguage.length) {
          possibleLangKeys = possibleLangKeys.concat($fallbackLanguage);
        }
        for (var j = 0, d = possibleLangKeys.length; j < d; j++) {
          var possibleLangKey = possibleLangKeys[j];
          if ($translationTable[possibleLangKey]) {
            if (typeof $translationTable[possibleLangKey][translationId] !== 'undefined') {
              result = determineTranslationInstant(translationId, interpolateParams, interpolationId, uses);
            }
          }
          if (typeof result !== 'undefined') {
            break;
          }
        }

        if (!result && result !== '') {
          if ($notFoundIndicatorLeft || $notFoundIndicatorRight) {
            result = applyNotFoundIndicators(translationId);
          } else {
            // Return translation of default interpolator if not found anything.
            result = defaultInterpolator.interpolate(translationId, interpolateParams);
            if ($missingTranslationHandlerFactory && !pendingLoader) {
              result = translateByHandler(translationId, interpolateParams);
            }
          }
        }

        return result;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#versionInfo
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns the current version information for the angular-translate library
       *
       * @return {string} angular-translate version
       */
      $translate.versionInfo = function () {
        return version;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#loaderCache
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns the defined loaderCache.
       *
       * @return {boolean|string|object} current value of loaderCache
       */
      $translate.loaderCache = function () {
        return loaderCache;
      };

      // internal purpose only
      $translate.directivePriority = function () {
        return directivePriority;
      };

      // internal purpose only
      $translate.statefulFilter = function () {
        return statefulFilter;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#isReady
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns whether the service is "ready" to translate (i.e. loading 1st language).
       *
       * See also {@link pascalprecht.translate.$translate#methods_onReady onReady()}.
       *
       * @return {boolean} current value of ready
       */
      $translate.isReady = function () {
        return $isReady;
      };

      var $onReadyDeferred = $q.defer();
      $onReadyDeferred.promise.then(function () {
        $isReady = true;
      });

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#onReady
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * Returns whether the service is "ready" to translate (i.e. loading 1st language).
       *
       * See also {@link pascalprecht.translate.$translate#methods_isReady isReady()}.
       *
       * @param {Function=} fn Function to invoke when service is ready
       * @return {object} Promise resolved when service is ready
       */
      $translate.onReady = function (fn) {
        var deferred = $q.defer();
        if (angular.isFunction(fn)) {
          deferred.promise.then(fn);
        }
        if ($isReady) {
          deferred.resolve();
        } else {
          $onReadyDeferred.promise.then(deferred.resolve);
        }
        return deferred.promise;
      };

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translate#getAvailableLanguageKeys
       * @methodOf pascalprecht.translate.$translate
       *
       * @description
       * This function simply returns the registered language keys being defined before in the config phase
       * With this, an application can use the array to provide a language selection dropdown or similar
       * without any additional effort
       *
       * @returns {object} returns the list of possibly registered language keys and mapping or null if not defined
       */
      $translate.getAvailableLanguageKeys = function () {
        if ($availableLanguageKeys.length > 0) {
          return $availableLanguageKeys;
        }
        return null;
      };

      // Whenever $translateReady is being fired, this will ensure the state of $isReady
      var globalOnReadyListener = $rootScope.$on('$translateReady', function () {
        $onReadyDeferred.resolve();
        globalOnReadyListener(); // one time only
        globalOnReadyListener = null;
      });
      var globalOnChangeListener = $rootScope.$on('$translateChangeEnd', function () {
        $onReadyDeferred.resolve();
        globalOnChangeListener(); // one time only
        globalOnChangeListener = null;
      });

      if ($loaderFactory) {

        // If at least one async loader is defined and there are no
        // (default) translations available we should try to load them.
        if (angular.equals($translationTable, {})) {
          if ($translate.use()) {
            $translate.use($translate.use());
          }
        }

        // Also, if there are any fallback language registered, we start
        // loading them asynchronously as soon as we can.
        if ($fallbackLanguage && $fallbackLanguage.length) {
          var processAsyncResult = function (translation) {
            translations(translation.key, translation.table);
            $rootScope.$emit('$translateChangeEnd', { language: translation.key });
            return translation;
          };
          for (var i = 0, len = $fallbackLanguage.length; i < len; i++) {
            var fallbackLanguageId = $fallbackLanguage[i];
            if ($forceAsyncReloadEnabled || !$translationTable[fallbackLanguageId]) {
              langPromises[fallbackLanguageId] = loadAsync(fallbackLanguageId).then(processAsyncResult);
            }
          }
        }
      } else {
        $rootScope.$emit('$translateReady', { language: $translate.use() });
      }

      return $translate;
    }
  ];
}

$translate.displayName = 'displayName';

/**
 * @ngdoc object
 * @name pascalprecht.translate.$translateDefaultInterpolation
 * @requires $interpolate
 *
 * @description
 * Uses angular's `$interpolate` services to interpolate strings against some values.
 *
 * Be aware to configure a proper sanitization strategy.
 *
 * See also:
 * * {@link pascalprecht.translate.$translateSanitization}
 *
 * @return {object} $translateDefaultInterpolation Interpolator service
 */
angular.module('pascalprecht.translate').factory('$translateDefaultInterpolation', $translateDefaultInterpolation);

function $translateDefaultInterpolation ($interpolate, $translateSanitization) {

  'use strict';

  var $translateInterpolator = {},
      $locale,
      $identifier = 'default';

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateDefaultInterpolation#setLocale
   * @methodOf pascalprecht.translate.$translateDefaultInterpolation
   *
   * @description
   * Sets current locale (this is currently not use in this interpolation).
   *
   * @param {string} locale Language key or locale.
   */
  $translateInterpolator.setLocale = function (locale) {
    $locale = locale;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateDefaultInterpolation#getInterpolationIdentifier
   * @methodOf pascalprecht.translate.$translateDefaultInterpolation
   *
   * @description
   * Returns an identifier for this interpolation service.
   *
   * @returns {string} $identifier
   */
  $translateInterpolator.getInterpolationIdentifier = function () {
    return $identifier;
  };

  /**
   * @deprecated will be removed in 3.0
   * @see {@link pascalprecht.translate.$translateSanitization}
   */
  $translateInterpolator.useSanitizeValueStrategy = function (value) {
    $translateSanitization.useStrategy(value);
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateDefaultInterpolation#interpolate
   * @methodOf pascalprecht.translate.$translateDefaultInterpolation
   *
   * @description
   * Interpolates given value agains given interpolate params using angulars
   * `$interpolate` service.
   *
   * Since AngularJS 1.5, `value` must not be a string but can be anything input.
   *
   * @returns {string} interpolated string.
   */
  $translateInterpolator.interpolate = function (value, interpolationParams) {
    interpolationParams = interpolationParams || {};
    interpolationParams = $translateSanitization.sanitize(interpolationParams, 'params');

    var interpolatedText;
    if (angular.isNumber(value)) {
      // numbers are safe
      interpolatedText = '' + value;
    } else if (angular.isString(value)) {
      // strings must be interpolated (that's the job here)
      interpolatedText = $interpolate(value)(interpolationParams);
      interpolatedText = $translateSanitization.sanitize(interpolatedText, 'text');
    } else {
      // neither a number or a string, cant interpolate => empty string
      interpolatedText = '';
    }

    return interpolatedText;
  };

  return $translateInterpolator;
}

$translateDefaultInterpolation.displayName = '$translateDefaultInterpolation';

angular.module('pascalprecht.translate').constant('$STORAGE_KEY', 'NG_TRANSLATE_LANG_KEY');

angular.module('pascalprecht.translate')
/**
 * @ngdoc directive
 * @name pascalprecht.translate.directive:translate
 * @requires $compile
 * @requires $filter
 * @requires $interpolate
 * @restrict AE
 *
 * @description
 * Translates given translation id either through attribute or DOM content.
 * Internally it uses `translate` filter to translate translation id. It possible to
 * pass an optional `translate-values` object literal as string into translation id.
 *
 * @param {string=} translate Translation id which could be either string or interpolated string.
 * @param {string=} translate-values Values to pass into translation id. Can be passed as object literal string or interpolated object.
 * @param {string=} translate-attr-ATTR translate Translation id and put it into ATTR attribute.
 * @param {string=} translate-default will be used unless translation was successful
 * @param {boolean=} translate-compile (default true if present) defines locally activation of {@link pascalprecht.translate.$translateProvider#methods_usePostCompiling}
 * @param {boolean=} translate-keep-content (default true if present) defines that in case a KEY could not be translated, that the existing content is left in the innerHTML}
 *
 * @example
   <example module="ngView">
    <file name="index.html">
      <div ng-controller="TranslateCtrl">

        <pre translate="TRANSLATION_ID"></pre>
        <pre translate>TRANSLATION_ID</pre>
        <pre translate translate-attr-title="TRANSLATION_ID"></pre>
        <pre translate="{{translationId}}"></pre>
        <pre translate>{{translationId}}</pre>
        <pre translate="WITH_VALUES" translate-values="{value: 5}"></pre>
        <pre translate translate-values="{value: 5}">WITH_VALUES</pre>
        <pre translate="WITH_VALUES" translate-values="{{values}}"></pre>
        <pre translate translate-values="{{values}}">WITH_VALUES</pre>
        <pre translate translate-attr-title="WITH_VALUES" translate-values="{{values}}"></pre>
        <pre translate="WITH_CAMEL_CASE_KEY" translate-value-camel-case-key="Hi"></pre>

      </div>
    </file>
    <file name="script.js">
      angular.module('ngView', ['pascalprecht.translate'])

      .config(function ($translateProvider) {

        $translateProvider.translations('en',{
          'TRANSLATION_ID': 'Hello there!',
          'WITH_VALUES': 'The following value is dynamic: {{value}}',
          'WITH_CAMEL_CASE_KEY': 'The interpolation key is camel cased: {{camelCaseKey}}'
        }).preferredLanguage('en');

      });

      angular.module('ngView').controller('TranslateCtrl', function ($scope) {
        $scope.translationId = 'TRANSLATION_ID';

        $scope.values = {
          value: 78
        };
      });
    </file>
    <file name="scenario.js">
      it('should translate', function () {
        inject(function ($rootScope, $compile) {
          $rootScope.translationId = 'TRANSLATION_ID';

          element = $compile('<p translate="TRANSLATION_ID"></p>')($rootScope);
          $rootScope.$digest();
          expect(element.text()).toBe('Hello there!');

          element = $compile('<p translate="{{translationId}}"></p>')($rootScope);
          $rootScope.$digest();
          expect(element.text()).toBe('Hello there!');

          element = $compile('<p translate>TRANSLATION_ID</p>')($rootScope);
          $rootScope.$digest();
          expect(element.text()).toBe('Hello there!');

          element = $compile('<p translate>{{translationId}}</p>')($rootScope);
          $rootScope.$digest();
          expect(element.text()).toBe('Hello there!');

          element = $compile('<p translate translate-attr-title="TRANSLATION_ID"></p>')($rootScope);
          $rootScope.$digest();
          expect(element.attr('title')).toBe('Hello there!');

          element = $compile('<p translate="WITH_CAMEL_CASE_KEY" translate-value-camel-case-key="Hello"></p>')($rootScope);
          $rootScope.$digest();
          expect(element.text()).toBe('The interpolation key is camel cased: Hello');
        });
      });
    </file>
   </example>
 */
.directive('translate', translateDirective);
function translateDirective($translate, $q, $interpolate, $compile, $parse, $rootScope) {

  'use strict';

  /**
   * @name trim
   * @private
   *
   * @description
   * trim polyfill
   *
   * @returns {string} The string stripped of whitespace from both ends
   */
  var trim = function() {
    return this.toString().replace(/^\s+|\s+$/g, '');
  };

  return {
    restrict: 'AE',
    scope: true,
    priority: $translate.directivePriority(),
    compile: function (tElement, tAttr) {

      var translateValuesExist = (tAttr.translateValues) ?
        tAttr.translateValues : undefined;

      var translateInterpolation = (tAttr.translateInterpolation) ?
        tAttr.translateInterpolation : undefined;

      var translateValueExist = tElement[0].outerHTML.match(/translate-value-+/i);

      var interpolateRegExp = '^(.*)(' + $interpolate.startSymbol() + '.*' + $interpolate.endSymbol() + ')(.*)',
          watcherRegExp = '^(.*)' + $interpolate.startSymbol() + '(.*)' + $interpolate.endSymbol() + '(.*)';

      return function linkFn(scope, iElement, iAttr) {

        scope.interpolateParams = {};
        scope.preText = '';
        scope.postText = '';
        scope.translateNamespace = getTranslateNamespace(scope);
        var translationIds = {};

        var initInterpolationParams = function (interpolateParams, iAttr, tAttr) {
          // initial setup
          if (iAttr.translateValues) {
            angular.extend(interpolateParams, $parse(iAttr.translateValues)(scope.$parent));
          }
          // initially fetch all attributes if existing and fill the params
          if (translateValueExist) {
            for (var attr in tAttr) {
              if (Object.prototype.hasOwnProperty.call(iAttr, attr) && attr.substr(0, 14) === 'translateValue' && attr !== 'translateValues') {
                var attributeName = angular.lowercase(attr.substr(14, 1)) + attr.substr(15);
                interpolateParams[attributeName] = tAttr[attr];
              }
            }
          }
        };

        // Ensures any change of the attribute "translate" containing the id will
        // be re-stored to the scope's "translationId".
        // If the attribute has no content, the element's text value (white spaces trimmed off) will be used.
        var observeElementTranslation = function (translationId) {

          // Remove any old watcher
          if (angular.isFunction(observeElementTranslation._unwatchOld)) {
            observeElementTranslation._unwatchOld();
            observeElementTranslation._unwatchOld = undefined;
          }

          if (angular.equals(translationId , '') || !angular.isDefined(translationId)) {
            var iElementText = trim.apply(iElement.text());

            // Resolve translation id by inner html if required
            var interpolateMatches = iElementText.match(interpolateRegExp);
            // Interpolate translation id if required
            if (angular.isArray(interpolateMatches)) {
              scope.preText = interpolateMatches[1];
              scope.postText = interpolateMatches[3];
              translationIds.translate = $interpolate(interpolateMatches[2])(scope.$parent);
              var watcherMatches = iElementText.match(watcherRegExp);
              if (angular.isArray(watcherMatches) && watcherMatches[2] && watcherMatches[2].length) {
                observeElementTranslation._unwatchOld = scope.$watch(watcherMatches[2], function (newValue) {
                  translationIds.translate = newValue;
                  updateTranslations();
                });
              }
            } else {
              // do not assigne the translation id if it is empty.
              translationIds.translate = !iElementText ? undefined : iElementText;
            }
          } else {
            translationIds.translate = translationId;
          }
          updateTranslations();
        };

        var observeAttributeTranslation = function (translateAttr) {
          iAttr.$observe(translateAttr, function (translationId) {
            translationIds[translateAttr] = translationId;
            updateTranslations();
          });
        };

        // initial setup with values
        initInterpolationParams(scope.interpolateParams, iAttr, tAttr);

        var firstAttributeChangedEvent = true;
        iAttr.$observe('translate', function (translationId) {
          if (typeof translationId === 'undefined') {
            // case of element "<translate>xyz</translate>"
            observeElementTranslation('');
          } else {
            // case of regular attribute
            if (translationId !== '' || !firstAttributeChangedEvent) {
              translationIds.translate = translationId;
              updateTranslations();
            }
          }
          firstAttributeChangedEvent = false;
        });

        for (var translateAttr in iAttr) {
          if (iAttr.hasOwnProperty(translateAttr) && translateAttr.substr(0, 13) === 'translateAttr') {
            observeAttributeTranslation(translateAttr);
          }
        }

        iAttr.$observe('translateDefault', function (value) {
          scope.defaultText = value;
          updateTranslations();
        });

        if (translateValuesExist) {
          iAttr.$observe('translateValues', function (interpolateParams) {
            if (interpolateParams) {
              scope.$parent.$watch(function () {
                angular.extend(scope.interpolateParams, $parse(interpolateParams)(scope.$parent));
              });
            }
          });
        }

        if (translateValueExist) {
          var observeValueAttribute = function (attrName) {
            iAttr.$observe(attrName, function (value) {
              var attributeName = angular.lowercase(attrName.substr(14, 1)) + attrName.substr(15);
              scope.interpolateParams[attributeName] = value;
            });
          };
          for (var attr in iAttr) {
            if (Object.prototype.hasOwnProperty.call(iAttr, attr) && attr.substr(0, 14) === 'translateValue' && attr !== 'translateValues') {
              observeValueAttribute(attr);
            }
          }
        }

        // Master update function
        var updateTranslations = function () {
          for (var key in translationIds) {
            if (translationIds.hasOwnProperty(key) && translationIds[key] !== undefined) {
              updateTranslation(key, translationIds[key], scope, scope.interpolateParams, scope.defaultText, scope.translateNamespace);
            }
          }
        };

        // Put translation processing function outside loop
        var updateTranslation = function(translateAttr, translationId, scope, interpolateParams, defaultTranslationText, translateNamespace) {
          if (translationId) {
            // if translation id starts with '.' and translateNamespace given, prepend namespace
            if (translateNamespace && translationId.charAt(0) === '.') {
              translationId = translateNamespace + translationId;
            }

            $translate(translationId, interpolateParams, translateInterpolation, defaultTranslationText, scope.translateLanguage)
              .then(function (translation) {
                applyTranslation(translation, scope, true, translateAttr);
              }, function (translationId) {
                applyTranslation(translationId, scope, false, translateAttr);
              });
          } else {
            // as an empty string cannot be translated, we can solve this using successful=false
            applyTranslation(translationId, scope, false, translateAttr);
          }
        };

        var applyTranslation = function (value, scope, successful, translateAttr) {
          if (!successful) {
            if (typeof scope.defaultText !== 'undefined') {
              value = scope.defaultText;
            }
          }
          if (translateAttr === 'translate') {
            // default translate into innerHTML
            if (successful || (!successful && !$translate.isKeepContent() && typeof iAttr.translateKeepContent === 'undefined')) {
              iElement.empty().append(scope.preText + value + scope.postText);
            }
            var globallyEnabled = $translate.isPostCompilingEnabled();
            var locallyDefined = typeof tAttr.translateCompile !== 'undefined';
            var locallyEnabled = locallyDefined && tAttr.translateCompile !== 'false';
            if ((globallyEnabled && !locallyDefined) || locallyEnabled) {
              $compile(iElement.contents())(scope);
            }
          } else {
            // translate attribute
            var attributeName = iAttr.$attr[translateAttr];
            if (attributeName.substr(0, 5) === 'data-') {
              // ensure html5 data prefix is stripped
              attributeName = attributeName.substr(5);
            }
            attributeName = attributeName.substr(15);
            iElement.attr(attributeName, value);
          }
        };

        if (translateValuesExist || translateValueExist || iAttr.translateDefault) {
          scope.$watch('interpolateParams', updateTranslations, true);
        }

        // Replaced watcher on translateLanguage with event listener
        var unbindTranslateLanguage = scope.$on('translateLanguageChanged', updateTranslations);

        // Ensures the text will be refreshed after the current language was changed
        // w/ $translate.use(...)
        var unbind = $rootScope.$on('$translateChangeSuccess', updateTranslations);

        // ensure translation will be looked up at least one
        if (iElement.text().length) {
          if (iAttr.translate) {
            observeElementTranslation(iAttr.translate);
          } else {
            observeElementTranslation('');
          }
        } else if (iAttr.translate) {
          // ensure attribute will be not skipped
          observeElementTranslation(iAttr.translate);
        }
        updateTranslations();
        scope.$on('$destroy', function(){
          unbindTranslateLanguage();
          unbind();
        });
      };
    }
  };
}

/**
 * Returns the scope's namespace.
 * @private
 * @param scope
 * @returns {string}
 */
function getTranslateNamespace(scope) {
  'use strict';
  if (scope.translateNamespace) {
    return scope.translateNamespace;
  }
  if (scope.$parent) {
    return getTranslateNamespace(scope.$parent);
  }
}

translateDirective.displayName = 'translateDirective';

angular.module('pascalprecht.translate')
/**
 * @ngdoc directive
 * @name pascalprecht.translate.directive:translateCloak
 * @requires $rootScope
 * @requires $translate
 * @restrict A
 *
 * $description
 * Adds a `translate-cloak` class name to the given element where this directive
 * is applied initially and removes it, once a loader has finished loading.
 *
 * This directive can be used to prevent initial flickering when loading translation
 * data asynchronously.
 *
 * The class name is defined in
 * {@link pascalprecht.translate.$translateProvider#cloakClassName $translate.cloakClassName()}.
 *
 * @param {string=} translate-cloak If a translationId is provided, it will be used for showing
 *                                  or hiding the cloak. Basically it relies on the translation
 *                                  resolve.
 */
.directive('translateCloak', translateCloakDirective);

function translateCloakDirective($translate, $rootScope) {

  'use strict';

  return {
    compile: function (tElement) {
      var applyCloak = function () {
        tElement.addClass($translate.cloakClassName());
      },
      removeCloak = function () {
        tElement.removeClass($translate.cloakClassName());
      };
      $translate.onReady(function () {
        removeCloak();
      });
      applyCloak();

      return function linkFn(scope, iElement, iAttr) {
        if (iAttr.translateCloak && iAttr.translateCloak.length) {
          // Register a watcher for the defined translation allowing a fine tuned cloak
          iAttr.$observe('translateCloak', function (translationId) {
            $translate(translationId).then(removeCloak, applyCloak);
          });
          // Register for change events as this is being another indicicator revalidating the cloak)
          $rootScope.$on('$translateChangeSuccess', function () {
            $translate(iAttr.translateCloak).then(removeCloak, applyCloak);
          });
        }
      };
    }
  };
}

translateCloakDirective.displayName = 'translateCloakDirective';

angular.module('pascalprecht.translate')
/**
 * @ngdoc directive
 * @name pascalprecht.translate.directive:translateNamespace
 * @restrict A
 *
 * @description
 * Translates given translation id either through attribute or DOM content.
 * Internally it uses `translate` filter to translate translation id. It possible to
 * pass an optional `translate-values` object literal as string into translation id.
 *
 * @param {string=} translate namespace name which could be either string or interpolated string.
 *
 * @example
   <example module="ngView">
    <file name="index.html">
      <div translate-namespace="CONTENT">

        <div>
            <h1 translate>.HEADERS.TITLE</h1>
            <h1 translate>.HEADERS.WELCOME</h1>
        </div>

        <div translate-namespace=".HEADERS">
            <h1 translate>.TITLE</h1>
            <h1 translate>.WELCOME</h1>
        </div>

      </div>
    </file>
    <file name="script.js">
      angular.module('ngView', ['pascalprecht.translate'])

      .config(function ($translateProvider) {

        $translateProvider.translations('en',{
          'TRANSLATION_ID': 'Hello there!',
          'CONTENT': {
            'HEADERS': {
                TITLE: 'Title'
            }
          },
          'CONTENT.HEADERS.WELCOME': 'Welcome'
        }).preferredLanguage('en');

      });

    </file>
   </example>
 */
.directive('translateNamespace', translateNamespaceDirective);

function translateNamespaceDirective() {

  'use strict';

  return {
    restrict: 'A',
    scope: true,
    compile: function () {
      return {
        pre: function (scope, iElement, iAttrs) {
          scope.translateNamespace = getTranslateNamespace(scope);

          if (scope.translateNamespace && iAttrs.translateNamespace.charAt(0) === '.') {
            scope.translateNamespace += iAttrs.translateNamespace;
          } else {
            scope.translateNamespace = iAttrs.translateNamespace;
          }
        }
      };
    }
  };
}

/**
 * Returns the scope's namespace.
 * @private
 * @param scope
 * @returns {string}
 */
function getTranslateNamespace(scope) {
  'use strict';
  if (scope.translateNamespace) {
    return scope.translateNamespace;
  }
  if (scope.$parent) {
    return getTranslateNamespace(scope.$parent);
  }
}

translateNamespaceDirective.displayName = 'translateNamespaceDirective';

angular.module('pascalprecht.translate')
/**
 * @ngdoc directive
 * @name pascalprecht.translate.directive:translateLanguage
 * @restrict A
 *
 * @description
 * Forces the language to the directives in the underlying scope.
 *
 * @param {string=} translate language that will be negotiated.
 *
 * @example
   <example module="ngView">
    <file name="index.html">
      <div>

        <div>
            <h1 translate>HELLO</h1>
        </div>

        <div translate-language="de">
            <h1 translate>HELLO</h1>
        </div>

      </div>
    </file>
    <file name="script.js">
      angular.module('ngView', ['pascalprecht.translate'])

      .config(function ($translateProvider) {

        $translateProvider
          .translations('en',{
            'HELLO': 'Hello world!'
          })
          .translations('de',{
            'HELLO': 'Hallo Welt!'
          })
          .preferredLanguage('en');

      });

    </file>
   </example>
 */
.directive('translateLanguage', translateLanguageDirective);

function translateLanguageDirective() {

  'use strict';

  return {
    restrict: 'A',
    scope: true,
    compile: function () {
      return function linkFn(scope, iElement, iAttrs) {

        iAttrs.$observe('translateLanguage', function (newTranslateLanguage) {
          scope.translateLanguage = newTranslateLanguage;
        });

        scope.$watch('translateLanguage', function(){
          scope.$broadcast('translateLanguageChanged');
        });
      };
    }
  };
}

translateLanguageDirective.displayName = 'translateLanguageDirective';

angular.module('pascalprecht.translate')
/**
 * @ngdoc filter
 * @name pascalprecht.translate.filter:translate
 * @requires $parse
 * @requires pascalprecht.translate.$translate
 * @function
 *
 * @description
 * Uses `$translate` service to translate contents. Accepts interpolate parameters
 * to pass dynamized values though translation.
 *
 * @param {string} translationId A translation id to be translated.
 * @param {*=} interpolateParams Optional object literal (as hash or string) to pass values into translation.
 *
 * @returns {string} Translated text.
 *
 * @example
   <example module="ngView">
    <file name="index.html">
      <div ng-controller="TranslateCtrl">

        <pre>{{ 'TRANSLATION_ID' | translate }}</pre>
        <pre>{{ translationId | translate }}</pre>
        <pre>{{ 'WITH_VALUES' | translate:'{value: 5}' }}</pre>
        <pre>{{ 'WITH_VALUES' | translate:values }}</pre>

      </div>
    </file>
    <file name="script.js">
      angular.module('ngView', ['pascalprecht.translate'])

      .config(function ($translateProvider) {

        $translateProvider.translations('en', {
          'TRANSLATION_ID': 'Hello there!',
          'WITH_VALUES': 'The following value is dynamic: {{value}}'
        });
        $translateProvider.preferredLanguage('en');

      });

      angular.module('ngView').controller('TranslateCtrl', function ($scope) {
        $scope.translationId = 'TRANSLATION_ID';

        $scope.values = {
          value: 78
        };
      });
    </file>
   </example>
 */
.filter('translate', translateFilterFactory);

function translateFilterFactory($parse, $translate) {

  'use strict';

  var translateFilter = function (translationId, interpolateParams, interpolation, forceLanguage) {
    if (!angular.isObject(interpolateParams)) {
      interpolateParams = $parse(interpolateParams)(this);
    }

    return $translate.instant(translationId, interpolateParams, interpolation, forceLanguage);
  };

  if ($translate.statefulFilter()) {
    translateFilter.$stateful = true;
  }

  return translateFilter;
}

translateFilterFactory.displayName = 'translateFilterFactory';

angular.module('pascalprecht.translate')

/**
 * @ngdoc object
 * @name pascalprecht.translate.$translationCache
 * @requires $cacheFactory
 *
 * @description
 * The first time a translation table is used, it is loaded in the translation cache for quick retrieval. You
 * can load translation tables directly into the cache by consuming the
 * `$translationCache` service directly.
 *
 * @return {object} $cacheFactory object.
 */
  .factory('$translationCache', $translationCache);

function $translationCache($cacheFactory) {

  'use strict';

  return $cacheFactory('translations');
}

$translationCache.displayName = '$translationCache';
return 'pascalprecht.translate';

}));

/*!
 * angular-translate - v2.11.0 - 2016-03-20
 * 
 * Copyright (c) 2016 The angular-translate team, Pascal Precht; Licensed MIT
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module unless amdModuleId is set
    define([], function () {
      return (factory());
    });
  } else if (typeof exports === 'object') {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory();
  } else {
    factory();
  }
}(this, function () {

angular.module('pascalprecht.translate')
/**
 * @ngdoc object
 * @name pascalprecht.translate.$translatePartialLoaderProvider
 *
 * @description
 * By using a $translatePartialLoaderProvider you can configure a list of a needed
 * translation parts directly during the configuration phase of your application's
 * lifetime. All parts you add by using this provider would be loaded by
 * angular-translate at the startup as soon as possible.
 */
  .provider('$translatePartialLoader', $translatePartialLoader);

function $translatePartialLoader() {

  'use strict';

  /**
   * @constructor
   * @name Part
   *
   * @description
   * Represents Part object to add and set parts at runtime.
   */
  function Part(name, priority) {
    this.name = name;
    this.isActive = true;
    this.tables = {};
    this.priority = priority || 0;
  }

  /**
   * @name parseUrl
   * @method
   *
   * @description
   * Returns a parsed url template string and replaces given target lang
   * and part name it.
   *
   * @param {string|function} urlTemplate - Either a string containing an url pattern (with
   *                                        '{part}' and '{lang}') or a function(part, lang)
   *                                        returning a string.
   * @param {string} targetLang - Language key for language to be used.
   * @return {string} Parsed url template string
   */
  Part.prototype.parseUrl = function(urlTemplate, targetLang) {
    if (angular.isFunction(urlTemplate)) {
      return urlTemplate(this.name, targetLang);
    }
    return urlTemplate.replace(/\{part\}/g, this.name).replace(/\{lang\}/g, targetLang);
  };

  Part.prototype.getTable = function(lang, $q, $http, $httpOptions, urlTemplate, errorHandler) {

    if (!this.tables[lang]) {
      var self = this;

      return $http(angular.extend({
        method : 'GET',
        url: this.parseUrl(urlTemplate, lang)
      }, $httpOptions))
        .then(function(result){
          self.tables[lang] = result.data;
          return result.data;
        }, function() {
          if (errorHandler) {
            return errorHandler(self.name, lang)
              .then(function(data) {
                self.tables[lang] = data;
                return data;
              }, function() {
                return $q.reject(self.name);
              });
          } else {
            return $q.reject(self.name);
          }
        });

    } else {
      return $q.when(this.tables[lang]);
    }
  };

  var parts = {};

  function hasPart(name) {
    return Object.prototype.hasOwnProperty.call(parts, name);
  }

  function isStringValid(str) {
    return angular.isString(str) && str !== '';
  }

  function isPartAvailable(name) {
    if (!isStringValid(name)) {
      throw new TypeError('Invalid type of a first argument, a non-empty string expected.');
    }

    return (hasPart(name) && parts[name].isActive);
  }

  function deepExtend(dst, src) {
    for (var property in src) {
      if (src[property] && src[property].constructor &&
       src[property].constructor === Object) {
        dst[property] = dst[property] || {};
        deepExtend(dst[property], src[property]);
      } else {
        dst[property] = src[property];
      }
    }
    return dst;
  }

  function getPrioritizedParts() {
    var prioritizedParts = [];
    for(var part in parts) {
      if (parts[part].isActive) {
        prioritizedParts.push(parts[part]);
      }
    }
    prioritizedParts.sort(function (a, b) {
      return a.priority - b.priority;
    });
    return prioritizedParts;
  }


  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translatePartialLoaderProvider#addPart
   * @methodOf pascalprecht.translate.$translatePartialLoaderProvider
   *
   * @description
   * Registers a new part of the translation table to be loaded once the
   * `angular-translate` gets into runtime phase. It does not actually load any
   * translation data, but only registers a part to be loaded in the future.
   *
   * @param {string} name A name of the part to add
   * @param {int} [priority=0] Sets the load priority of this part.
   *
   * @returns {object} $translatePartialLoaderProvider, so this method is chainable
   * @throws {TypeError} The method could throw a **TypeError** if you pass the param
   * of the wrong type. Please, note that the `name` param has to be a
   * non-empty **string**.
   */
  this.addPart = function(name, priority) {
    if (!isStringValid(name)) {
      throw new TypeError('Couldn\'t add part, part name has to be a string!');
    }

    if (!hasPart(name)) {
      parts[name] = new Part(name, priority);
    }
    parts[name].isActive = true;

    return this;
  };

  /**
   * @ngdocs function
   * @name pascalprecht.translate.$translatePartialLoaderProvider#setPart
   * @methodOf pascalprecht.translate.$translatePartialLoaderProvider
   *
   * @description
   * Sets a translation table to the specified part. This method does not make the
   * specified part available, but only avoids loading this part from the server.
   *
   * @param {string} lang A language of the given translation table
   * @param {string} part A name of the target part
   * @param {object} table A translation table to set to the specified part
   *
   * @return {object} $translatePartialLoaderProvider, so this method is chainable
   * @throws {TypeError} The method could throw a **TypeError** if you pass params
   * of the wrong type. Please, note that the `lang` and `part` params have to be a
   * non-empty **string**s and the `table` param has to be an object.
   */
  this.setPart = function (lang, part, table) {
    if (!isStringValid(lang)) {
      throw new TypeError('Couldn\'t set part.`lang` parameter has to be a string!');
    }
    if (!isStringValid(part)) {
      throw new TypeError('Couldn\'t set part.`part` parameter has to be a string!');
    }
    if (typeof table !== 'object' || table === null) {
      throw new TypeError('Couldn\'t set part. `table` parameter has to be an object!');
    }

    if (!hasPart(part)) {
      parts[part] = new Part(part);
      parts[part].isActive = false;
    }

    parts[part].tables[lang] = table;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translatePartialLoaderProvider#deletePart
   * @methodOf pascalprecht.translate.$translatePartialLoaderProvider
   *
   * @description
   * Removes the previously added part of the translation data. So, `angular-translate` will not
   * load it at the startup.
   *
   * @param {string} name A name of the part to delete
   *
   * @returns {object} $translatePartialLoaderProvider, so this method is chainable
   *
   * @throws {TypeError} The method could throw a **TypeError** if you pass the param of the wrong
   * type. Please, note that the `name` param has to be a non-empty **string**.
   */
  this.deletePart = function (name) {
    if (!isStringValid(name)) {
      throw new TypeError('Couldn\'t delete part, first arg has to be string.');
    }

    if (hasPart(name)) {
      parts[name].isActive = false;
    }

    return this;
  };


  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translatePartialLoaderProvider#isPartAvailable
   * @methodOf pascalprecht.translate.$translatePartialLoaderProvider
   *
   * @description
   * Checks if the specific part is available. A part becomes available after it was added by the
   * `addPart` method. Available parts would be loaded from the server once the `angular-translate`
   * asks the loader to that.
   *
   * @param {string} name A name of the part to check
   *
   * @returns {boolean} Returns **true** if the part is available now and **false** if not.
   *
   * @throws {TypeError} The method could throw a **TypeError** if you pass the param of the wrong
   * type. Please, note that the `name` param has to be a non-empty **string**.
   */
  this.isPartAvailable = isPartAvailable;

  /**
   * @ngdoc object
   * @name pascalprecht.translate.$translatePartialLoader
   *
   * @requires $q
   * @requires $http
   * @requires $injector
   * @requires $rootScope
   * @requires $translate
   *
   * @description
   *
   * @param {object} options Options object
   *
   * @throws {TypeError}
   */
  this.$get = ['$rootScope', '$injector', '$q', '$http',
  function($rootScope, $injector, $q, $http) {

    /**
     * @ngdoc event
     * @name pascalprecht.translate.$translatePartialLoader#$translatePartialLoaderStructureChanged
     * @eventOf pascalprecht.translate.$translatePartialLoader
     * @eventType broadcast on root scope
     *
     * @description
     * A $translatePartialLoaderStructureChanged event is called when a state of the loader was
     * changed somehow. It could mean either some part is added or some part is deleted. Anyway when
     * you get this event the translation table is not longer current and has to be updated.
     *
     * @param {string} name A name of the part which is a reason why the event was fired
     */

    var service = function(options) {
      if (!isStringValid(options.key)) {
        throw new TypeError('Unable to load data, a key is not a non-empty string.');
      }

      if (!isStringValid(options.urlTemplate) && !angular.isFunction(options.urlTemplate)) {
        throw new TypeError('Unable to load data, a urlTemplate is not a non-empty string or not a function.');
      }

      var errorHandler = options.loadFailureHandler;
      if (errorHandler !== undefined) {
        if (!angular.isString(errorHandler)) {
          throw new Error('Unable to load data, a loadFailureHandler is not a string.');
        } else {
          errorHandler = $injector.get(errorHandler);
        }
      }

      var loaders = [],
          prioritizedParts = getPrioritizedParts();

      angular.forEach(prioritizedParts, function(part) {
        loaders.push(
          part.getTable(options.key, $q, $http, options.$http, options.urlTemplate, errorHandler)
        );
        part.urlTemplate = options.urlTemplate;
      });

      return $q.all(loaders)
        .then(function() {
          var table = {};
          prioritizedParts = getPrioritizedParts();
          angular.forEach(prioritizedParts, function(part) {
            deepExtend(table, part.tables[options.key]);
          });
          return table;
        }, function() {
          return $q.reject(options.key);
        });
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translatePartialLoader#addPart
     * @methodOf pascalprecht.translate.$translatePartialLoader
     *
     * @description
     * Registers a new part of the translation table. This method does not actually perform any xhr
     * requests to get translation data. The new parts will be loaded in order of priority from the server next time
     * `angular-translate` asks the loader to load translations.
     *
     * @param {string} name A name of the part to add
     * @param {int} [priority=0] Sets the load priority of this part.
     *
     * @returns {object} $translatePartialLoader, so this method is chainable
     *
     * @fires {$translatePartialLoaderStructureChanged} The $translatePartialLoaderStructureChanged
     * event would be fired by this method in case the new part affected somehow on the loaders
     * state. This way it means that there are a new translation data available to be loaded from
     * the server.
     *
     * @throws {TypeError} The method could throw a **TypeError** if you pass the param of the wrong
     * type. Please, note that the `name` param has to be a non-empty **string**.
     */
    service.addPart = function(name, priority) {
      if (!isStringValid(name)) {
        throw new TypeError('Couldn\'t add part, first arg has to be a string');
      }

      if (!hasPart(name)) {
        parts[name] = new Part(name, priority);
        $rootScope.$emit('$translatePartialLoaderStructureChanged', name);
      } else if (!parts[name].isActive) {
        parts[name].isActive = true;
        $rootScope.$emit('$translatePartialLoaderStructureChanged', name);
      }

      return service;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translatePartialLoader#deletePart
     * @methodOf pascalprecht.translate.$translatePartialLoader
     *
     * @description
     * Deletes the previously added part of the translation data. The target part could be deleted
     * either logically or physically. When the data is deleted logically it is not actually deleted
     * from the browser, but the loader marks it as not active and prevents it from affecting on the
     * translations. If the deleted in such way part is added again, the loader will use the
     * previously loaded data rather than loading it from the server once more time. But if the data
     * is deleted physically, the loader will completely remove all information about it. So in case
     * of recycling this part will be loaded from the server again.
     *
     * @param {string} name A name of the part to delete
     * @param {boolean=} [removeData=false] An indicator if the loader has to remove a loaded
     * translation data physically. If the `removeData` if set to **false** the loaded data will not be
     * deleted physically and might be reused in the future to prevent an additional xhr requests.
     *
     * @returns {object} $translatePartialLoader, so this method is chainable
     *
     * @fires {$translatePartialLoaderStructureChanged} The $translatePartialLoaderStructureChanged
     * event would be fired by this method in case a part deletion process affects somehow on the
     * loaders state. This way it means that some part of the translation data is now deprecated and
     * the translation table has to be recompiled with the remaining translation parts.
     *
     * @throws {TypeError} The method could throw a **TypeError** if you pass some param of the
     * wrong type. Please, note that the `name` param has to be a non-empty **string** and
     * the `removeData` param has to be either **undefined** or **boolean**.
     */
    service.deletePart = function(name, removeData) {
      if (!isStringValid(name)) {
        throw new TypeError('Couldn\'t delete part, first arg has to be string');
      }

      if (removeData === undefined) {
        removeData = false;
      } else if (typeof removeData !== 'boolean') {
        throw new TypeError('Invalid type of a second argument, a boolean expected.');
      }

      if (hasPart(name)) {
        var wasActive = parts[name].isActive;
        if (removeData) {
          var $translate = $injector.get('$translate');
          var cache = $translate.loaderCache();
          if (typeof(cache) === 'string') {
            // getting on-demand instance of loader
            cache = $injector.get(cache);
          }
          // Purging items from cache...
          if (typeof(cache) === 'object') {
            angular.forEach(parts[name].tables, function(value, key) {
                cache.remove(parts[name].parseUrl(parts[name].urlTemplate, key));
            });
          }
          delete parts[name];
        } else {
          parts[name].isActive = false;
        }
        if (wasActive) {
          $rootScope.$emit('$translatePartialLoaderStructureChanged', name);
        }
      }

      return service;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translatePartialLoader#isPartLoaded
     * @methodOf pascalprecht.translate.$translatePartialLoader
     *
     * @description
     * Checks if the registered translation part is loaded into the translation table.
     *
     * @param {string} name A name of the part
     * @param {string} lang A key of the language
     *
     * @returns {boolean} Returns **true** if the translation of the part is loaded to the translation table and **false** if not.
     *
     * @throws {TypeError} The method could throw a **TypeError** if you pass the param of the wrong
     * type. Please, note that the `name` and `lang` params have to be non-empty **string**.
     */
    service.isPartLoaded = function(name, lang) {
      return angular.isDefined(parts[name]) && angular.isDefined(parts[name].tables[lang]);
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translatePartialLoader#getRegisteredParts
     * @methodOf pascalprecht.translate.$translatePartialLoader
     *
     * @description
     * Gets names of the parts that were added with the `addPart`.
     *
     * @returns {array} Returns array of registered parts, if none were registered then an empty array is returned.
     */
    service.getRegisteredParts = function() {
      var registeredParts = [];
      angular.forEach(parts, function(p){
        if(p.isActive) {
          registeredParts.push(p.name);
        }
      });
      return registeredParts;
    };



    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translatePartialLoader#isPartAvailable
     * @methodOf pascalprecht.translate.$translatePartialLoader
     *
     * @description
     * Checks if a target translation part is available. The part becomes available just after it was
     * added by the `addPart` method. Part's availability does not mean that it was loaded from the
     * server, but only that it was added to the loader. The available part might be loaded next
     * time the loader is called.
     *
     * @param {string} name A name of the part to delete
     *
     * @returns {boolean} Returns **true** if the part is available now and **false** if not.
     *
     * @throws {TypeError} The method could throw a **TypeError** if you pass the param of the wrong
     * type. Please, note that the `name` param has to be a non-empty **string**.
     */
    service.isPartAvailable = isPartAvailable;

    return service;

  }];

}

$translatePartialLoader.displayName = '$translatePartialLoader';
return 'pascalprecht.translate';

}));

/*!
 * angular-translate - v2.11.0 - 2016-03-20
 * 
 * Copyright (c) 2016 The angular-translate team, Pascal Precht; Licensed MIT
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module unless amdModuleId is set
    define([], function () {
      return (factory());
    });
  } else if (typeof exports === 'object') {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory();
  } else {
    factory();
  }
}(this, function () {

$translateUrlLoader.$inject = ['$q', '$http'];
angular.module('pascalprecht.translate')
/**
 * @ngdoc object
 * @name pascalprecht.translate.$translateUrlLoader
 * @requires $q
 * @requires $http
 *
 * @description
 * Creates a loading function for a typical dynamic url pattern:
 * "locale.php?lang=en_US", "locale.php?lang=de_DE", "locale.php?language=nl_NL" etc.
 * Prefixing the specified url, the current requested, language id will be applied
 * with "?{queryParameter}={key}".
 * Using this service, the response of these urls must be an object of
 * key-value pairs.
 *
 * @param {object} options Options object, which gets the url, key and
 * optional queryParameter ('lang' is used by default).
 */
.factory('$translateUrlLoader', $translateUrlLoader);

function $translateUrlLoader($q, $http) {

  'use strict';

  return function (options) {

    if (!options || !options.url) {
      throw new Error('Couldn\'t use urlLoader since no url is given!');
    }

    var requestParams = {};

    requestParams[options.queryParameter || 'lang'] = options.key;

    return $http(angular.extend({
      url: options.url,
      params: requestParams,
      method: 'GET'
    }, options.$http))
      .then(function(result) {
        return result.data;
      }, function () {
        return $q.reject(options.key);
      });
  };
}

$translateUrlLoader.displayName = '$translateUrlLoader';
return 'pascalprecht.translate';

}));

/*!
 * angular-ui-mask
 * https://github.com/angular-ui/ui-mask
 * Version: 1.8.7 - 2016-07-26T15:59:07.992Z
 * License: MIT
 */


(function () { 
'use strict';
/*
 Attaches input mask onto input element
 */
angular.module('ui.mask', [])
        .value('uiMaskConfig', {
            maskDefinitions: {
                '9': /\d/,
                'A': /[a-zA-Z]/,
                '*': /[a-zA-Z0-9]/
            },
            clearOnBlur: true,
            clearOnBlurPlaceholder: false,
            escChar: '\\',
            eventsToHandle: ['input', 'keyup', 'click', 'focus'],
            addDefaultPlaceholder: true,
            allowInvalidValue: false
        })
        .provider('uiMask.Config', function() {
            var options = {};

            this.maskDefinitions = function(maskDefinitions) {
                return options.maskDefinitions = maskDefinitions;
            };
            this.clearOnBlur = function(clearOnBlur) {
                return options.clearOnBlur = clearOnBlur;
            };
            this.clearOnBlurPlaceholder = function(clearOnBlurPlaceholder) {
                return options.clearOnBlurPlaceholder = clearOnBlurPlaceholder;
            };
            this.eventsToHandle = function(eventsToHandle) {
                return options.eventsToHandle = eventsToHandle;
            };
            this.addDefaultPlaceholder = function(addDefaultPlaceholder) {
                return options.addDefaultPlaceholder = addDefaultPlaceholder;
            };
            this.allowInvalidValue = function(allowInvalidValue) {
                return options.allowInvalidValue = allowInvalidValue;
            };
            this.$get = ['uiMaskConfig', function(uiMaskConfig) {
                var tempOptions = uiMaskConfig;
                for(var prop in options) {
                    if (angular.isObject(options[prop]) && !angular.isArray(options[prop])) {
                        angular.extend(tempOptions[prop], options[prop]);
                    } else {
                        tempOptions[prop] = options[prop];
                    }
                }

                return tempOptions;
            }];
        })
        .directive('uiMask', ['uiMask.Config', function(maskConfig) {
                function isFocused (elem) {
                  return elem === document.activeElement && (!document.hasFocus || document.hasFocus()) && !!(elem.type || elem.href || ~elem.tabIndex);
                }

                return {
                    priority: 100,
                    require: 'ngModel',
                    restrict: 'A',
                    compile: function uiMaskCompilingFunction() {
                        var options = angular.copy(maskConfig);

                        return function uiMaskLinkingFunction(scope, iElement, iAttrs, controller) {
                            var maskProcessed = false, eventsBound = false,
                                    maskCaretMap, maskPatterns, maskPlaceholder, maskComponents,
                                    // Minimum required length of the value to be considered valid
                                    minRequiredLength,
                                    value, valueMasked, isValid,
                                    // Vars for initializing/uninitializing
                                    originalPlaceholder = iAttrs.placeholder,
                                    originalMaxlength = iAttrs.maxlength,
                                    // Vars used exclusively in eventHandler()
                                    oldValue, oldValueUnmasked, oldCaretPosition, oldSelectionLength,
                                    // Used for communicating if a backspace operation should be allowed between
                                    // keydownHandler and eventHandler
                                    preventBackspace;

                            var originalIsEmpty = controller.$isEmpty;
                            controller.$isEmpty = function(value) {
                                if (maskProcessed) {
                                    return originalIsEmpty(unmaskValue(value || ''));
                                } else {
                                    return originalIsEmpty(value);
                                }
                            };

                            function initialize(maskAttr) {
                                if (!angular.isDefined(maskAttr)) {
                                    return uninitialize();
                                }
                                processRawMask(maskAttr);
                                if (!maskProcessed) {
                                    return uninitialize();
                                }
                                initializeElement();
                                bindEventListeners();
                                return true;
                            }

                            function initPlaceholder(placeholderAttr) {
                                if ( ! placeholderAttr) {
                                    return;
                                }

                                maskPlaceholder = placeholderAttr;

                                // If the mask is processed, then we need to update the value
                                // but don't set the value if there is nothing entered into the element
                                // and there is a placeholder attribute on the element because that
                                // will only set the value as the blank maskPlaceholder
                                // and override the placeholder on the element
                                if (maskProcessed && !(iElement.val().length === 0 && angular.isDefined(iAttrs.placeholder))) {
                                    iElement.val(maskValue(unmaskValue(iElement.val())));
                                }
                            }

                            function initPlaceholderChar() {
                                return initialize(iAttrs.uiMask);
                            }

                            var modelViewValue = false;
                            iAttrs.$observe('modelViewValue', function(val) {
                                if (val === 'true') {
                                    modelViewValue = true;
                                }
                            });

                            iAttrs.$observe('allowInvalidValue', function(val) {
                                linkOptions.allowInvalidValue = val === ''
                                    ? true
                                    : !!val;
                                formatter(controller.$modelValue);
                            });

                            function formatter(fromModelValue) {
                                if (!maskProcessed) {
                                    return fromModelValue;
                                }
                                value = unmaskValue(fromModelValue || '');
                                isValid = validateValue(value);
                                controller.$setValidity('mask', isValid);

                                if (!value.length) return undefined;
                                if (isValid || linkOptions.allowInvalidValue) {
                                    return maskValue(value);
                                } else {
                                    return undefined;
                                }
                            }

                            function parser(fromViewValue) {
                                if (!maskProcessed) {
                                    return fromViewValue;
                                }
                                value = unmaskValue(fromViewValue || '');
                                isValid = validateValue(value);
                                // We have to set viewValue manually as the reformatting of the input
                                // value performed by eventHandler() doesn't happen until after
                                // this parser is called, which causes what the user sees in the input
                                // to be out-of-sync with what the controller's $viewValue is set to.
                                controller.$viewValue = value.length ? maskValue(value) : '';
                                controller.$setValidity('mask', isValid);

                                if (isValid || linkOptions.allowInvalidValue) {
                                    return modelViewValue ? controller.$viewValue : value;
                                }
                            }

                            var linkOptions = {};

                            if (iAttrs.uiOptions) {
                                linkOptions = scope.$eval('[' + iAttrs.uiOptions + ']');
                                if (angular.isObject(linkOptions[0])) {
                                    // we can't use angular.copy nor angular.extend, they lack the power to do a deep merge
                                    linkOptions = (function(original, current) {
                                        for (var i in original) {
                                            if (Object.prototype.hasOwnProperty.call(original, i)) {
                                                if (current[i] === undefined) {
                                                    current[i] = angular.copy(original[i]);
                                                } else {
                                                    if (angular.isObject(current[i]) && !angular.isArray(current[i])) {
                                                        current[i] = angular.extend({}, original[i], current[i]);
                                                    }
                                                }
                                            }
                                        }
                                        return current;
                                    })(options, linkOptions[0]);
                                } else {
                                    linkOptions = options;  //gotta be a better way to do this..
                                }
                            } else {
                                linkOptions = options;
                            }

                            iAttrs.$observe('uiMask', initialize);
                            if (angular.isDefined(iAttrs.uiMaskPlaceholder)) {
                                iAttrs.$observe('uiMaskPlaceholder', initPlaceholder);
                            }
                            else {
                                iAttrs.$observe('placeholder', initPlaceholder);
                            }
                            if (angular.isDefined(iAttrs.uiMaskPlaceholderChar)) {
                                iAttrs.$observe('uiMaskPlaceholderChar', initPlaceholderChar);
                            }

                            controller.$formatters.unshift(formatter);
                            controller.$parsers.unshift(parser);

                            function uninitialize() {
                                maskProcessed = false;
                                unbindEventListeners();

                                if (angular.isDefined(originalPlaceholder)) {
                                    iElement.attr('placeholder', originalPlaceholder);
                                } else {
                                    iElement.removeAttr('placeholder');
                                }

                                if (angular.isDefined(originalMaxlength)) {
                                    iElement.attr('maxlength', originalMaxlength);
                                } else {
                                    iElement.removeAttr('maxlength');
                                }

                                iElement.val(controller.$modelValue);
                                controller.$viewValue = controller.$modelValue;
                                return false;
                            }

                            function initializeElement() {
                                value = oldValueUnmasked = unmaskValue(controller.$modelValue || '');
                                valueMasked = oldValue = maskValue(value);
                                isValid = validateValue(value);
                                if (iAttrs.maxlength) { // Double maxlength to allow pasting new val at end of mask
                                    iElement.attr('maxlength', maskCaretMap[maskCaretMap.length - 1] * 2);
                                }
                                if ( ! originalPlaceholder && linkOptions.addDefaultPlaceholder) {
                                    iElement.attr('placeholder', maskPlaceholder);
                                }
                                var viewValue = controller.$modelValue;
                                var idx = controller.$formatters.length;
                                while(idx--) {
                                    viewValue = controller.$formatters[idx](viewValue);
                                }
                                controller.$viewValue = viewValue || '';
                                controller.$render();
                                // Not using $setViewValue so we don't clobber the model value and dirty the form
                                // without any kind of user interaction.
                            }

                            function bindEventListeners() {
                                if (eventsBound) {
                                    return;
                                }
                                iElement.bind('blur', blurHandler);
                                iElement.bind('mousedown mouseup', mouseDownUpHandler);
                                iElement.bind('keydown', keydownHandler);
                                iElement.bind(linkOptions.eventsToHandle.join(' '), eventHandler);
                                eventsBound = true;
                            }

                            function unbindEventListeners() {
                                if (!eventsBound) {
                                    return;
                                }
                                iElement.unbind('blur', blurHandler);
                                iElement.unbind('mousedown', mouseDownUpHandler);
                                iElement.unbind('mouseup', mouseDownUpHandler);
                                iElement.unbind('keydown', keydownHandler);
                                iElement.unbind('input', eventHandler);
                                iElement.unbind('keyup', eventHandler);
                                iElement.unbind('click', eventHandler);
                                iElement.unbind('focus', eventHandler);
                                eventsBound = false;
                            }

                            function validateValue(value) {
                                // Zero-length value validity is ngRequired's determination
                                return value.length ? value.length >= minRequiredLength : true;
                            }

                            function unmaskValue(value) {
                                var valueUnmasked = '',
                                    input = iElement[0],
                                    maskPatternsCopy = maskPatterns.slice(),
                                    selectionStart = oldCaretPosition,
                                    selectionEnd = selectionStart + getSelectionLength(input),
                                    valueOffset, valueDelta, tempValue = '';
                                // Preprocess by stripping mask components from value
                                value = value.toString();
                                valueOffset = 0;
                                valueDelta = value.length - maskPlaceholder.length;
                                angular.forEach(maskComponents, function(component) {
                                    var position = component.position;
                                    //Only try and replace the component if the component position is not within the selected range
                                    //If component was in selected range then it was removed with the user input so no need to try and remove that component
                                    if (!(position >= selectionStart && position < selectionEnd)) {
                                        if (position >= selectionStart) {
                                            position += valueDelta;
                                        }
                                        if (value.substring(position, position + component.value.length) === component.value) {
                                            tempValue += value.slice(valueOffset, position);// + value.slice(position + component.value.length);
                                            valueOffset = position + component.value.length;
                                        }
                                    }
                                });
                                value = tempValue + value.slice(valueOffset);
                                angular.forEach(value.split(''), function(chr) {
                                    if (maskPatternsCopy.length && maskPatternsCopy[0].test(chr)) {
                                        valueUnmasked += chr;
                                        maskPatternsCopy.shift();
                                    }
                                });

                                return valueUnmasked;
                            }

                            function maskValue(unmaskedValue) {
                                var valueMasked = '',
                                        maskCaretMapCopy = maskCaretMap.slice();

                                angular.forEach(maskPlaceholder.split(''), function(chr, i) {
                                    if (unmaskedValue.length && i === maskCaretMapCopy[0]) {
                                        valueMasked += unmaskedValue.charAt(0) || '_';
                                        unmaskedValue = unmaskedValue.substr(1);
                                        maskCaretMapCopy.shift();
                                    }
                                    else {
                                        valueMasked += chr;
                                    }
                                });
                                return valueMasked;
                            }

                            function getPlaceholderChar(i) {
                                var placeholder = angular.isDefined(iAttrs.uiMaskPlaceholder) ? iAttrs.uiMaskPlaceholder : iAttrs.placeholder,
                                    defaultPlaceholderChar;

                                if (angular.isDefined(placeholder) && placeholder[i]) {
                                    return placeholder[i];
                                } else {
                                    defaultPlaceholderChar = angular.isDefined(iAttrs.uiMaskPlaceholderChar) && iAttrs.uiMaskPlaceholderChar ? iAttrs.uiMaskPlaceholderChar : '_';
                                    return (defaultPlaceholderChar.toLowerCase() === 'space') ? ' ' : defaultPlaceholderChar[0];
                                }
                            }

                            // Generate array of mask components that will be stripped from a masked value
                            // before processing to prevent mask components from being added to the unmasked value.
                            // E.g., a mask pattern of '+7 9999' won't have the 7 bleed into the unmasked value.
                            function getMaskComponents() {
                                var maskPlaceholderChars = maskPlaceholder.split(''),
                                        maskPlaceholderCopy, components;

                                //maskCaretMap can have bad values if the input has the ui-mask attribute implemented as an obversable property, e.g. the demo page
                                if (maskCaretMap && !isNaN(maskCaretMap[0])) {
                                    //Instead of trying to manipulate the RegEx based on the placeholder characters
                                    //we can simply replace the placeholder characters based on the already built
                                    //maskCaretMap to underscores and leave the original working RegEx to get the proper
                                    //mask components
                                    angular.forEach(maskCaretMap, function(value) {
                                        maskPlaceholderChars[value] = '_';
                                    });
                                }
                                maskPlaceholderCopy = maskPlaceholderChars.join('');
                                components = maskPlaceholderCopy.replace(/[_]+/g, '_').split('_');
                                components = components.filter(function(s) {
                                    return s !== '';
                                });

                                // need a string search offset in cases where the mask contains multiple identical components
                                // E.g., a mask of 99.99.99-999.99
                                var offset = 0;
                                return components.map(function(c) {
                                    var componentPosition = maskPlaceholderCopy.indexOf(c, offset);
                                    offset = componentPosition + 1;
                                    return {
                                        value: c,
                                        position: componentPosition
                                    };
                                });
                            }

                            function processRawMask(mask) {
                                var characterCount = 0;

                                maskCaretMap = [];
                                maskPatterns = [];
                                maskPlaceholder = '';

                                if (angular.isString(mask)) {
                                    minRequiredLength = 0;

                                    var isOptional = false,
                                            numberOfOptionalCharacters = 0,
                                            splitMask = mask.split('');

                                    var inEscape = false;
                                    angular.forEach(splitMask, function(chr, i) {
                                        if (inEscape) {
                                            inEscape = false;
                                            maskPlaceholder += chr;
                                            characterCount++;
                                        }
                                        else if (linkOptions.escChar === chr) {
                                            inEscape = true;
                                        }
                                        else if (linkOptions.maskDefinitions[chr]) {
                                            maskCaretMap.push(characterCount);

                                            maskPlaceholder += getPlaceholderChar(i - numberOfOptionalCharacters);
                                            maskPatterns.push(linkOptions.maskDefinitions[chr]);

                                            characterCount++;
                                            if (!isOptional) {
                                                minRequiredLength++;
                                            }

                                            isOptional = false;
                                        }
                                        else if (chr === '?') {
                                            isOptional = true;
                                            numberOfOptionalCharacters++;
                                        }
                                        else {
                                            maskPlaceholder += chr;
                                            characterCount++;
                                        }
                                    });
                                }
                                // Caret position immediately following last position is valid.
                                maskCaretMap.push(maskCaretMap.slice().pop() + 1);

                                maskComponents = getMaskComponents();
                                maskProcessed = maskCaretMap.length > 1 ? true : false;
                            }

                            var prevValue = iElement.val();
                            function blurHandler() {
                                if (linkOptions.clearOnBlur || ((linkOptions.clearOnBlurPlaceholder) && (value.length === 0) && iAttrs.placeholder)) {
                                    oldCaretPosition = 0;
                                    oldSelectionLength = 0;
                                    if (!isValid || value.length === 0) {
                                        valueMasked = '';
                                        iElement.val('');
                                        scope.$apply(function() {
                                            //only $setViewValue when not $pristine to avoid changing $pristine state.
                                            if (!controller.$pristine) {
                                                controller.$setViewValue('');
                                            }
                                        });
                                    }
                                }
                                //Check for different value and trigger change.
                                //Check for different value and trigger change.
                                if (value !== prevValue) {
                                    // #157 Fix the bug from the trigger when backspacing exactly on the first letter (emptying the field)
                                    // and then blurring out.
                                    // Angular uses html element and calls setViewValue(element.value.trim()), setting it to the trimmed mask
                                    // when it should be empty
                                    var currentVal = iElement.val();
                                    var isTemporarilyEmpty = value === '' && currentVal && angular.isDefined(iAttrs.uiMaskPlaceholderChar) && iAttrs.uiMaskPlaceholderChar === 'space';
                                    if(isTemporarilyEmpty) {
                                        iElement.val('');
                                    }
                                    triggerChangeEvent(iElement[0]);
                                    if(isTemporarilyEmpty) {
                                        iElement.val(currentVal);
                                    }
                                }
                                prevValue = value;
                            }

                            function triggerChangeEvent(element) {
                                var change;
                                if (angular.isFunction(window.Event) && !element.fireEvent) {
                                    // modern browsers and Edge
                                    try {
                                        change = new Event('change', {
                                            view: window,
                                            bubbles: true,
                                            cancelable: false
                                        });
                                    } catch (ex) {
                                        //this is for certain mobile browsers that have the Event object
                                        //but don't support the Event constructor #168
                                        change = document.createEvent('HTMLEvents');
                                        change.initEvent('change', false, true);
                                    } finally {
                                        element.dispatchEvent(change);
                                    }
                                } else if ('createEvent' in document) {
                                    // older browsers
                                    change = document.createEvent('HTMLEvents');
                                    change.initEvent('change', false, true);
                                    element.dispatchEvent(change);
                                }
                                else if (element.fireEvent) {
                                    // IE <= 11
                                    element.fireEvent('onchange');
                                }
                            }

                            function mouseDownUpHandler(e) {
                                if (e.type === 'mousedown') {
                                    iElement.bind('mouseout', mouseoutHandler);
                                } else {
                                    iElement.unbind('mouseout', mouseoutHandler);
                                }
                            }

                            iElement.bind('mousedown mouseup', mouseDownUpHandler);

                            function mouseoutHandler() {
                                /*jshint validthis: true */
                                oldSelectionLength = getSelectionLength(this);
                                iElement.unbind('mouseout', mouseoutHandler);
                            }

                            function keydownHandler(e) {
                                /*jshint validthis: true */
                                var isKeyBackspace = e.which === 8,
                                caretPos = getCaretPosition(this) - 1 || 0, //value in keydown is pre change so bump caret position back to simulate post change
                                isCtrlZ = e.which === 90 && e.ctrlKey; //ctrl+z pressed

                                if (isKeyBackspace) {
                                    while(caretPos >= 0) {
                                        if (isValidCaretPosition(caretPos)) {
                                            //re-adjust the caret position.
                                            //Increment to account for the initial decrement to simulate post change caret position
                                            setCaretPosition(this, caretPos + 1);
                                            break;
                                        }
                                        caretPos--;
                                    }
                                    preventBackspace = caretPos === -1;
                                }

                                if (isCtrlZ) {
                                    // prevent IE bug - value should be returned to initial state
                                    iElement.val('');
                                    e.preventDefault();
                                }
                            }

                            function eventHandler(e) {
                                /*jshint validthis: true */
                                e = e || {};
                                // Allows more efficient minification
                                var eventWhich = e.which,
                                        eventType = e.type;

                                // Prevent shift and ctrl from mucking with old values
                                if (eventWhich === 16 || eventWhich === 91) {
                                    return;
                                }

                                var val = iElement.val(),
                                        valOld = oldValue,
                                        valMasked,
                                        valAltered = false,
                                        valUnmasked = unmaskValue(val),
                                        valUnmaskedOld = oldValueUnmasked,
                                        caretPos = getCaretPosition(this) || 0,
                                        caretPosOld = oldCaretPosition || 0,
                                        caretPosDelta = caretPos - caretPosOld,
                                        caretPosMin = maskCaretMap[0],
                                        caretPosMax = maskCaretMap[valUnmasked.length] || maskCaretMap.slice().shift(),
                                        selectionLenOld = oldSelectionLength || 0,
                                        isSelected = getSelectionLength(this) > 0,
                                        wasSelected = selectionLenOld > 0,
                                        // Case: Typing a character to overwrite a selection
                                        isAddition = (val.length > valOld.length) || (selectionLenOld && val.length > valOld.length - selectionLenOld),
                                        // Case: Delete and backspace behave identically on a selection
                                        isDeletion = (val.length < valOld.length) || (selectionLenOld && val.length === valOld.length - selectionLenOld),
                                        isSelection = (eventWhich >= 37 && eventWhich <= 40) && e.shiftKey, // Arrow key codes

                                        isKeyLeftArrow = eventWhich === 37,
                                        // Necessary due to "input" event not providing a key code
                                        isKeyBackspace = eventWhich === 8 || (eventType !== 'keyup' && isDeletion && (caretPosDelta === -1)),
                                        isKeyDelete = eventWhich === 46 || (eventType !== 'keyup' && isDeletion && (caretPosDelta === 0) && !wasSelected),
                                        // Handles cases where caret is moved and placed in front of invalid maskCaretMap position. Logic below
                                        // ensures that, on click or leftward caret placement, caret is moved leftward until directly right of
                                        // non-mask character. Also applied to click since users are (arguably) more likely to backspace
                                        // a character when clicking within a filled input.
                                        caretBumpBack = (isKeyLeftArrow || isKeyBackspace || eventType === 'click') && caretPos > caretPosMin;

                                oldSelectionLength = getSelectionLength(this);

                                // These events don't require any action
                                if (isSelection || (isSelected && (eventType === 'click' || eventType === 'keyup' || eventType === 'focus'))) {
                                    return;
                                }

                                if (isKeyBackspace && preventBackspace) {
                                    iElement.val(maskPlaceholder);
                                    // This shouldn't be needed but for some reason after aggressive backspacing the controller $viewValue is incorrect.
                                    // This keeps the $viewValue updated and correct.
                                    scope.$apply(function () {
                                        controller.$setViewValue(''); // $setViewValue should be run in angular context, otherwise the changes will be invisible to angular and user code.
                                    });
                                    setCaretPosition(this, caretPosOld);
                                    return;
                                }

                                // Value Handling
                                // ==============

                                // User attempted to delete but raw value was unaffected--correct this grievous offense
                                if ((eventType === 'input') && isDeletion && !wasSelected && valUnmasked === valUnmaskedOld) {
                                    while (isKeyBackspace && caretPos > caretPosMin && !isValidCaretPosition(caretPos)) {
                                        caretPos--;
                                    }
                                    while (isKeyDelete && caretPos < caretPosMax && maskCaretMap.indexOf(caretPos) === -1) {
                                        caretPos++;
                                    }
                                    var charIndex = maskCaretMap.indexOf(caretPos);
                                    // Strip out non-mask character that user would have deleted if mask hadn't been in the way.
                                    valUnmasked = valUnmasked.substring(0, charIndex) + valUnmasked.substring(charIndex + 1);

                                    // If value has not changed, don't want to call $setViewValue, may be caused by IE raising input event due to placeholder
                                    if (valUnmasked !== valUnmaskedOld)
                                        valAltered = true;
                                }

                                // Update values
                                valMasked = maskValue(valUnmasked);

                                oldValue = valMasked;
                                oldValueUnmasked = valUnmasked;

                                //additional check to fix the problem where the viewValue is out of sync with the value of the element.
                                //better fix for commit 2a83b5fb8312e71d220a497545f999fc82503bd9 (I think)
                                if (!valAltered && val.length > valMasked.length)
                                    valAltered = true;

                                iElement.val(valMasked);

                                //we need this check.  What could happen if you don't have it is that you'll set the model value without the user
                                //actually doing anything.  Meaning, things like pristine and touched will be set.
                                if (valAltered) {
                                    scope.$apply(function () {
                                        controller.$setViewValue(valMasked); // $setViewValue should be run in angular context, otherwise the changes will be invisible to angular and user code.
                                    });
                                }

                                // Caret Repositioning
                                // ===================

                                // Ensure that typing always places caret ahead of typed character in cases where the first char of
                                // the input is a mask char and the caret is placed at the 0 position.
                                if (isAddition && (caretPos <= caretPosMin)) {
                                    caretPos = caretPosMin + 1;
                                }

                                if (caretBumpBack) {
                                    caretPos--;
                                }

                                // Make sure caret is within min and max position limits
                                caretPos = caretPos > caretPosMax ? caretPosMax : caretPos < caretPosMin ? caretPosMin : caretPos;

                                // Scoot the caret back or forth until it's in a non-mask position and within min/max position limits
                                while (!isValidCaretPosition(caretPos) && caretPos > caretPosMin && caretPos < caretPosMax) {
                                    caretPos += caretBumpBack ? -1 : 1;
                                }

                                if ((caretBumpBack && caretPos < caretPosMax) || (isAddition && !isValidCaretPosition(caretPosOld))) {
                                    caretPos++;
                                }
                                oldCaretPosition = caretPos;
                                setCaretPosition(this, caretPos);
                            }

                            function isValidCaretPosition(pos) {
                                return maskCaretMap.indexOf(pos) > -1;
                            }

                            function getCaretPosition(input) {
                                if (!input)
                                    return 0;
                                if (input.selectionStart !== undefined) {
                                    return input.selectionStart;
                                } else if (document.selection) {
                                    if (isFocused(iElement[0])) {
                                        // Curse you IE
                                        input.focus();
                                        var selection = document.selection.createRange();
                                        selection.moveStart('character', input.value ? -input.value.length : 0);
                                        return selection.text.length;
                                    }
                                }
                                return 0;
                            }

                            function setCaretPosition(input, pos) {
                                if (!input)
                                    return 0;
                                if (input.offsetWidth === 0 || input.offsetHeight === 0) {
                                    return; // Input's hidden
                                }
                                if (input.setSelectionRange) {
                                    if (isFocused(iElement[0])) {
                                        input.focus();
                                        input.setSelectionRange(pos, pos);
                                    }
                                }
                                else if (input.createTextRange) {
                                    // Curse you IE
                                    var range = input.createTextRange();
                                    range.collapse(true);
                                    range.moveEnd('character', pos);
                                    range.moveStart('character', pos);
                                    range.select();
                                }
                            }

                            function getSelectionLength(input) {
                                if (!input)
                                    return 0;
                                if (input.selectionStart !== undefined) {
                                    return (input.selectionEnd - input.selectionStart);
                                }
                                if (window.getSelection) {
                                    return (window.getSelection().toString().length);
                                }
                                if (document.selection) {
                                    return (document.selection.createRange().text.length);
                                }
                                return 0;
                            }

                            // https://developer.mozilla.org/en-US/docs/JavaScript/Reference/Global_Objects/Array/indexOf
                            if (!Array.prototype.indexOf) {
                                Array.prototype.indexOf = function(searchElement /*, fromIndex */) {
                                    if (this === null) {
                                        throw new TypeError();
                                    }
                                    var t = Object(this);
                                    var len = t.length >>> 0;
                                    if (len === 0) {
                                        return -1;
                                    }
                                    var n = 0;
                                    if (arguments.length > 1) {
                                        n = Number(arguments[1]);
                                        if (n !== n) { // shortcut for verifying if it's NaN
                                            n = 0;
                                        } else if (n !== 0 && n !== Infinity && n !== -Infinity) {
                                            n = (n > 0 || -1) * Math.floor(Math.abs(n));
                                        }
                                    }
                                    if (n >= len) {
                                        return -1;
                                    }
                                    var k = n >= 0 ? n : Math.max(len - Math.abs(n), 0);
                                    for (; k < len; k++) {
                                        if (k in t && t[k] === searchElement) {
                                            return k;
                                        }
                                    }
                                    return -1;
                                };
                            }

                        };
                    }
                };
            }
        ]);

}());
/*! jQuery UI - v1.12.1 - 2016-09-14
* http://jqueryui.com
* Includes: widget.js, position.js, data.js, disable-selection.js, effect.js, effects/effect-blind.js, effects/effect-bounce.js, effects/effect-clip.js, effects/effect-drop.js, effects/effect-explode.js, effects/effect-fade.js, effects/effect-fold.js, effects/effect-highlight.js, effects/effect-puff.js, effects/effect-pulsate.js, effects/effect-scale.js, effects/effect-shake.js, effects/effect-size.js, effects/effect-slide.js, effects/effect-transfer.js, focusable.js, form-reset-mixin.js, jquery-1-7.js, keycode.js, labels.js, scroll-parent.js, tabbable.js, unique-id.js, widgets/accordion.js, widgets/autocomplete.js, widgets/button.js, widgets/checkboxradio.js, widgets/controlgroup.js, widgets/datepicker.js, widgets/dialog.js, widgets/draggable.js, widgets/droppable.js, widgets/menu.js, widgets/mouse.js, widgets/progressbar.js, widgets/resizable.js, widgets/selectable.js, widgets/selectmenu.js, widgets/slider.js, widgets/sortable.js, widgets/spinner.js, widgets/tabs.js, widgets/tooltip.js
* Copyright jQuery Foundation and other contributors; Licensed MIT */

(function( factory ) {
	if ( typeof define === "function" && define.amd ) {

		// AMD. Register as an anonymous module.
		define([ "jquery" ], factory );
	} else {

		// Browser globals
		factory( jQuery );
	}
}(function( $ ) {

$.ui = $.ui || {};

var version = $.ui.version = "1.12.1";


/*!
 * jQuery UI Widget 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Widget
//>>group: Core
//>>description: Provides a factory for creating stateful widgets with a common API.
//>>docs: http://api.jqueryui.com/jQuery.widget/
//>>demos: http://jqueryui.com/widget/



var widgetUuid = 0;
var widgetSlice = Array.prototype.slice;

$.cleanData = ( function( orig ) {
	return function( elems ) {
		var events, elem, i;
		for ( i = 0; ( elem = elems[ i ] ) != null; i++ ) {
			try {

				// Only trigger remove when necessary to save time
				events = $._data( elem, "events" );
				if ( events && events.remove ) {
					$( elem ).triggerHandler( "remove" );
				}

			// Http://bugs.jquery.com/ticket/8235
			} catch ( e ) {}
		}
		orig( elems );
	};
} )( $.cleanData );

$.widget = function( name, base, prototype ) {
	var existingConstructor, constructor, basePrototype;

	// ProxiedPrototype allows the provided prototype to remain unmodified
	// so that it can be used as a mixin for multiple widgets (#8876)
	var proxiedPrototype = {};

	var namespace = name.split( "." )[ 0 ];
	name = name.split( "." )[ 1 ];
	var fullName = namespace + "-" + name;

	if ( !prototype ) {
		prototype = base;
		base = $.Widget;
	}

	if ( $.isArray( prototype ) ) {
		prototype = $.extend.apply( null, [ {} ].concat( prototype ) );
	}

	// Create selector for plugin
	$.expr[ ":" ][ fullName.toLowerCase() ] = function( elem ) {
		return !!$.data( elem, fullName );
	};

	$[ namespace ] = $[ namespace ] || {};
	existingConstructor = $[ namespace ][ name ];
	constructor = $[ namespace ][ name ] = function( options, element ) {

		// Allow instantiation without "new" keyword
		if ( !this._createWidget ) {
			return new constructor( options, element );
		}

		// Allow instantiation without initializing for simple inheritance
		// must use "new" keyword (the code above always passes args)
		if ( arguments.length ) {
			this._createWidget( options, element );
		}
	};

	// Extend with the existing constructor to carry over any static properties
	$.extend( constructor, existingConstructor, {
		version: prototype.version,

		// Copy the object used to create the prototype in case we need to
		// redefine the widget later
		_proto: $.extend( {}, prototype ),

		// Track widgets that inherit from this widget in case this widget is
		// redefined after a widget inherits from it
		_childConstructors: []
	} );

	basePrototype = new base();

	// We need to make the options hash a property directly on the new instance
	// otherwise we'll modify the options hash on the prototype that we're
	// inheriting from
	basePrototype.options = $.widget.extend( {}, basePrototype.options );
	$.each( prototype, function( prop, value ) {
		if ( !$.isFunction( value ) ) {
			proxiedPrototype[ prop ] = value;
			return;
		}
		proxiedPrototype[ prop ] = ( function() {
			function _super() {
				return base.prototype[ prop ].apply( this, arguments );
			}

			function _superApply( args ) {
				return base.prototype[ prop ].apply( this, args );
			}

			return function() {
				var __super = this._super;
				var __superApply = this._superApply;
				var returnValue;

				this._super = _super;
				this._superApply = _superApply;

				returnValue = value.apply( this, arguments );

				this._super = __super;
				this._superApply = __superApply;

				return returnValue;
			};
		} )();
	} );
	constructor.prototype = $.widget.extend( basePrototype, {

		// TODO: remove support for widgetEventPrefix
		// always use the name + a colon as the prefix, e.g., draggable:start
		// don't prefix for widgets that aren't DOM-based
		widgetEventPrefix: existingConstructor ? ( basePrototype.widgetEventPrefix || name ) : name
	}, proxiedPrototype, {
		constructor: constructor,
		namespace: namespace,
		widgetName: name,
		widgetFullName: fullName
	} );

	// If this widget is being redefined then we need to find all widgets that
	// are inheriting from it and redefine all of them so that they inherit from
	// the new version of this widget. We're essentially trying to replace one
	// level in the prototype chain.
	if ( existingConstructor ) {
		$.each( existingConstructor._childConstructors, function( i, child ) {
			var childPrototype = child.prototype;

			// Redefine the child widget using the same prototype that was
			// originally used, but inherit from the new version of the base
			$.widget( childPrototype.namespace + "." + childPrototype.widgetName, constructor,
				child._proto );
		} );

		// Remove the list of existing child constructors from the old constructor
		// so the old child constructors can be garbage collected
		delete existingConstructor._childConstructors;
	} else {
		base._childConstructors.push( constructor );
	}

	$.widget.bridge( name, constructor );

	return constructor;
};

$.widget.extend = function( target ) {
	var input = widgetSlice.call( arguments, 1 );
	var inputIndex = 0;
	var inputLength = input.length;
	var key;
	var value;

	for ( ; inputIndex < inputLength; inputIndex++ ) {
		for ( key in input[ inputIndex ] ) {
			value = input[ inputIndex ][ key ];
			if ( input[ inputIndex ].hasOwnProperty( key ) && value !== undefined ) {

				// Clone objects
				if ( $.isPlainObject( value ) ) {
					target[ key ] = $.isPlainObject( target[ key ] ) ?
						$.widget.extend( {}, target[ key ], value ) :

						// Don't extend strings, arrays, etc. with objects
						$.widget.extend( {}, value );

				// Copy everything else by reference
				} else {
					target[ key ] = value;
				}
			}
		}
	}
	return target;
};

$.widget.bridge = function( name, object ) {
	var fullName = object.prototype.widgetFullName || name;
	$.fn[ name ] = function( options ) {
		var isMethodCall = typeof options === "string";
		var args = widgetSlice.call( arguments, 1 );
		var returnValue = this;

		if ( isMethodCall ) {

			// If this is an empty collection, we need to have the instance method
			// return undefined instead of the jQuery instance
			if ( !this.length && options === "instance" ) {
				returnValue = undefined;
			} else {
				this.each( function() {
					var methodValue;
					var instance = $.data( this, fullName );

					if ( options === "instance" ) {
						returnValue = instance;
						return false;
					}

					if ( !instance ) {
						return $.error( "cannot call methods on " + name +
							" prior to initialization; " +
							"attempted to call method '" + options + "'" );
					}

					if ( !$.isFunction( instance[ options ] ) || options.charAt( 0 ) === "_" ) {
						return $.error( "no such method '" + options + "' for " + name +
							" widget instance" );
					}

					methodValue = instance[ options ].apply( instance, args );

					if ( methodValue !== instance && methodValue !== undefined ) {
						returnValue = methodValue && methodValue.jquery ?
							returnValue.pushStack( methodValue.get() ) :
							methodValue;
						return false;
					}
				} );
			}
		} else {

			// Allow multiple hashes to be passed on init
			if ( args.length ) {
				options = $.widget.extend.apply( null, [ options ].concat( args ) );
			}

			this.each( function() {
				var instance = $.data( this, fullName );
				if ( instance ) {
					instance.option( options || {} );
					if ( instance._init ) {
						instance._init();
					}
				} else {
					$.data( this, fullName, new object( options, this ) );
				}
			} );
		}

		return returnValue;
	};
};

$.Widget = function( /* options, element */ ) {};
$.Widget._childConstructors = [];

$.Widget.prototype = {
	widgetName: "widget",
	widgetEventPrefix: "",
	defaultElement: "<div>",

	options: {
		classes: {},
		disabled: false,

		// Callbacks
		create: null
	},

	_createWidget: function( options, element ) {
		element = $( element || this.defaultElement || this )[ 0 ];
		this.element = $( element );
		this.uuid = widgetUuid++;
		this.eventNamespace = "." + this.widgetName + this.uuid;

		this.bindings = $();
		this.hoverable = $();
		this.focusable = $();
		this.classesElementLookup = {};

		if ( element !== this ) {
			$.data( element, this.widgetFullName, this );
			this._on( true, this.element, {
				remove: function( event ) {
					if ( event.target === element ) {
						this.destroy();
					}
				}
			} );
			this.document = $( element.style ?

				// Element within the document
				element.ownerDocument :

				// Element is window or document
				element.document || element );
			this.window = $( this.document[ 0 ].defaultView || this.document[ 0 ].parentWindow );
		}

		this.options = $.widget.extend( {},
			this.options,
			this._getCreateOptions(),
			options );

		this._create();

		if ( this.options.disabled ) {
			this._setOptionDisabled( this.options.disabled );
		}

		this._trigger( "create", null, this._getCreateEventData() );
		this._init();
	},

	_getCreateOptions: function() {
		return {};
	},

	_getCreateEventData: $.noop,

	_create: $.noop,

	_init: $.noop,

	destroy: function() {
		var that = this;

		this._destroy();
		$.each( this.classesElementLookup, function( key, value ) {
			that._removeClass( value, key );
		} );

		// We can probably remove the unbind calls in 2.0
		// all event bindings should go through this._on()
		this.element
			.off( this.eventNamespace )
			.removeData( this.widgetFullName );
		this.widget()
			.off( this.eventNamespace )
			.removeAttr( "aria-disabled" );

		// Clean up events and states
		this.bindings.off( this.eventNamespace );
	},

	_destroy: $.noop,

	widget: function() {
		return this.element;
	},

	option: function( key, value ) {
		var options = key;
		var parts;
		var curOption;
		var i;

		if ( arguments.length === 0 ) {

			// Don't return a reference to the internal hash
			return $.widget.extend( {}, this.options );
		}

		if ( typeof key === "string" ) {

			// Handle nested keys, e.g., "foo.bar" => { foo: { bar: ___ } }
			options = {};
			parts = key.split( "." );
			key = parts.shift();
			if ( parts.length ) {
				curOption = options[ key ] = $.widget.extend( {}, this.options[ key ] );
				for ( i = 0; i < parts.length - 1; i++ ) {
					curOption[ parts[ i ] ] = curOption[ parts[ i ] ] || {};
					curOption = curOption[ parts[ i ] ];
				}
				key = parts.pop();
				if ( arguments.length === 1 ) {
					return curOption[ key ] === undefined ? null : curOption[ key ];
				}
				curOption[ key ] = value;
			} else {
				if ( arguments.length === 1 ) {
					return this.options[ key ] === undefined ? null : this.options[ key ];
				}
				options[ key ] = value;
			}
		}

		this._setOptions( options );

		return this;
	},

	_setOptions: function( options ) {
		var key;

		for ( key in options ) {
			this._setOption( key, options[ key ] );
		}

		return this;
	},

	_setOption: function( key, value ) {
		if ( key === "classes" ) {
			this._setOptionClasses( value );
		}

		this.options[ key ] = value;

		if ( key === "disabled" ) {
			this._setOptionDisabled( value );
		}

		return this;
	},

	_setOptionClasses: function( value ) {
		var classKey, elements, currentElements;

		for ( classKey in value ) {
			currentElements = this.classesElementLookup[ classKey ];
			if ( value[ classKey ] === this.options.classes[ classKey ] ||
					!currentElements ||
					!currentElements.length ) {
				continue;
			}

			// We are doing this to create a new jQuery object because the _removeClass() call
			// on the next line is going to destroy the reference to the current elements being
			// tracked. We need to save a copy of this collection so that we can add the new classes
			// below.
			elements = $( currentElements.get() );
			this._removeClass( currentElements, classKey );

			// We don't use _addClass() here, because that uses this.options.classes
			// for generating the string of classes. We want to use the value passed in from
			// _setOption(), this is the new value of the classes option which was passed to
			// _setOption(). We pass this value directly to _classes().
			elements.addClass( this._classes( {
				element: elements,
				keys: classKey,
				classes: value,
				add: true
			} ) );
		}
	},

	_setOptionDisabled: function( value ) {
		this._toggleClass( this.widget(), this.widgetFullName + "-disabled", null, !!value );

		// If the widget is becoming disabled, then nothing is interactive
		if ( value ) {
			this._removeClass( this.hoverable, null, "ui-state-hover" );
			this._removeClass( this.focusable, null, "ui-state-focus" );
		}
	},

	enable: function() {
		return this._setOptions( { disabled: false } );
	},

	disable: function() {
		return this._setOptions( { disabled: true } );
	},

	_classes: function( options ) {
		var full = [];
		var that = this;

		options = $.extend( {
			element: this.element,
			classes: this.options.classes || {}
		}, options );

		function processClassString( classes, checkOption ) {
			var current, i;
			for ( i = 0; i < classes.length; i++ ) {
				current = that.classesElementLookup[ classes[ i ] ] || $();
				if ( options.add ) {
					current = $( $.unique( current.get().concat( options.element.get() ) ) );
				} else {
					current = $( current.not( options.element ).get() );
				}
				that.classesElementLookup[ classes[ i ] ] = current;
				full.push( classes[ i ] );
				if ( checkOption && options.classes[ classes[ i ] ] ) {
					full.push( options.classes[ classes[ i ] ] );
				}
			}
		}

		this._on( options.element, {
			"remove": "_untrackClassesElement"
		} );

		if ( options.keys ) {
			processClassString( options.keys.match( /\S+/g ) || [], true );
		}
		if ( options.extra ) {
			processClassString( options.extra.match( /\S+/g ) || [] );
		}

		return full.join( " " );
	},

	_untrackClassesElement: function( event ) {
		var that = this;
		$.each( that.classesElementLookup, function( key, value ) {
			if ( $.inArray( event.target, value ) !== -1 ) {
				that.classesElementLookup[ key ] = $( value.not( event.target ).get() );
			}
		} );
	},

	_removeClass: function( element, keys, extra ) {
		return this._toggleClass( element, keys, extra, false );
	},

	_addClass: function( element, keys, extra ) {
		return this._toggleClass( element, keys, extra, true );
	},

	_toggleClass: function( element, keys, extra, add ) {
		add = ( typeof add === "boolean" ) ? add : extra;
		var shift = ( typeof element === "string" || element === null ),
			options = {
				extra: shift ? keys : extra,
				keys: shift ? element : keys,
				element: shift ? this.element : element,
				add: add
			};
		options.element.toggleClass( this._classes( options ), add );
		return this;
	},

	_on: function( suppressDisabledCheck, element, handlers ) {
		var delegateElement;
		var instance = this;

		// No suppressDisabledCheck flag, shuffle arguments
		if ( typeof suppressDisabledCheck !== "boolean" ) {
			handlers = element;
			element = suppressDisabledCheck;
			suppressDisabledCheck = false;
		}

		// No element argument, shuffle and use this.element
		if ( !handlers ) {
			handlers = element;
			element = this.element;
			delegateElement = this.widget();
		} else {
			element = delegateElement = $( element );
			this.bindings = this.bindings.add( element );
		}

		$.each( handlers, function( event, handler ) {
			function handlerProxy() {

				// Allow widgets to customize the disabled handling
				// - disabled as an array instead of boolean
				// - disabled class as method for disabling individual parts
				if ( !suppressDisabledCheck &&
						( instance.options.disabled === true ||
						$( this ).hasClass( "ui-state-disabled" ) ) ) {
					return;
				}
				return ( typeof handler === "string" ? instance[ handler ] : handler )
					.apply( instance, arguments );
			}

			// Copy the guid so direct unbinding works
			if ( typeof handler !== "string" ) {
				handlerProxy.guid = handler.guid =
					handler.guid || handlerProxy.guid || $.guid++;
			}

			var match = event.match( /^([\w:-]*)\s*(.*)$/ );
			var eventName = match[ 1 ] + instance.eventNamespace;
			var selector = match[ 2 ];

			if ( selector ) {
				delegateElement.on( eventName, selector, handlerProxy );
			} else {
				element.on( eventName, handlerProxy );
			}
		} );
	},

	_off: function( element, eventName ) {
		eventName = ( eventName || "" ).split( " " ).join( this.eventNamespace + " " ) +
			this.eventNamespace;
		element.off( eventName ).off( eventName );

		// Clear the stack to avoid memory leaks (#10056)
		this.bindings = $( this.bindings.not( element ).get() );
		this.focusable = $( this.focusable.not( element ).get() );
		this.hoverable = $( this.hoverable.not( element ).get() );
	},

	_delay: function( handler, delay ) {
		function handlerProxy() {
			return ( typeof handler === "string" ? instance[ handler ] : handler )
				.apply( instance, arguments );
		}
		var instance = this;
		return setTimeout( handlerProxy, delay || 0 );
	},

	_hoverable: function( element ) {
		this.hoverable = this.hoverable.add( element );
		this._on( element, {
			mouseenter: function( event ) {
				this._addClass( $( event.currentTarget ), null, "ui-state-hover" );
			},
			mouseleave: function( event ) {
				this._removeClass( $( event.currentTarget ), null, "ui-state-hover" );
			}
		} );
	},

	_focusable: function( element ) {
		this.focusable = this.focusable.add( element );
		this._on( element, {
			focusin: function( event ) {
				this._addClass( $( event.currentTarget ), null, "ui-state-focus" );
			},
			focusout: function( event ) {
				this._removeClass( $( event.currentTarget ), null, "ui-state-focus" );
			}
		} );
	},

	_trigger: function( type, event, data ) {
		var prop, orig;
		var callback = this.options[ type ];

		data = data || {};
		event = $.Event( event );
		event.type = ( type === this.widgetEventPrefix ?
			type :
			this.widgetEventPrefix + type ).toLowerCase();

		// The original event may come from any element
		// so we need to reset the target on the new event
		event.target = this.element[ 0 ];

		// Copy original event properties over to the new event
		orig = event.originalEvent;
		if ( orig ) {
			for ( prop in orig ) {
				if ( !( prop in event ) ) {
					event[ prop ] = orig[ prop ];
				}
			}
		}

		this.element.trigger( event, data );
		return !( $.isFunction( callback ) &&
			callback.apply( this.element[ 0 ], [ event ].concat( data ) ) === false ||
			event.isDefaultPrevented() );
	}
};

$.each( { show: "fadeIn", hide: "fadeOut" }, function( method, defaultEffect ) {
	$.Widget.prototype[ "_" + method ] = function( element, options, callback ) {
		if ( typeof options === "string" ) {
			options = { effect: options };
		}

		var hasOptions;
		var effectName = !options ?
			method :
			options === true || typeof options === "number" ?
				defaultEffect :
				options.effect || defaultEffect;

		options = options || {};
		if ( typeof options === "number" ) {
			options = { duration: options };
		}

		hasOptions = !$.isEmptyObject( options );
		options.complete = callback;

		if ( options.delay ) {
			element.delay( options.delay );
		}

		if ( hasOptions && $.effects && $.effects.effect[ effectName ] ) {
			element[ method ]( options );
		} else if ( effectName !== method && element[ effectName ] ) {
			element[ effectName ]( options.duration, options.easing, callback );
		} else {
			element.queue( function( next ) {
				$( this )[ method ]();
				if ( callback ) {
					callback.call( element[ 0 ] );
				}
				next();
			} );
		}
	};
} );

var widget = $.widget;


/*!
 * jQuery UI Position 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * http://api.jqueryui.com/position/
 */

//>>label: Position
//>>group: Core
//>>description: Positions elements relative to other elements.
//>>docs: http://api.jqueryui.com/position/
//>>demos: http://jqueryui.com/position/


( function() {
var cachedScrollbarWidth,
	max = Math.max,
	abs = Math.abs,
	rhorizontal = /left|center|right/,
	rvertical = /top|center|bottom/,
	roffset = /[\+\-]\d+(\.[\d]+)?%?/,
	rposition = /^\w+/,
	rpercent = /%$/,
	_position = $.fn.position;

function getOffsets( offsets, width, height ) {
	return [
		parseFloat( offsets[ 0 ] ) * ( rpercent.test( offsets[ 0 ] ) ? width / 100 : 1 ),
		parseFloat( offsets[ 1 ] ) * ( rpercent.test( offsets[ 1 ] ) ? height / 100 : 1 )
	];
}

function parseCss( element, property ) {
	return parseInt( $.css( element, property ), 10 ) || 0;
}

function getDimensions( elem ) {
	var raw = elem[ 0 ];
	if ( raw.nodeType === 9 ) {
		return {
			width: elem.width(),
			height: elem.height(),
			offset: { top: 0, left: 0 }
		};
	}
	if ( $.isWindow( raw ) ) {
		return {
			width: elem.width(),
			height: elem.height(),
			offset: { top: elem.scrollTop(), left: elem.scrollLeft() }
		};
	}
	if ( raw.preventDefault ) {
		return {
			width: 0,
			height: 0,
			offset: { top: raw.pageY, left: raw.pageX }
		};
	}
	return {
		width: elem.outerWidth(),
		height: elem.outerHeight(),
		offset: elem.offset()
	};
}

$.position = {
	scrollbarWidth: function() {
		if ( cachedScrollbarWidth !== undefined ) {
			return cachedScrollbarWidth;
		}
		var w1, w2,
			div = $( "<div " +
				"style='display:block;position:absolute;width:50px;height:50px;overflow:hidden;'>" +
				"<div style='height:100px;width:auto;'></div></div>" ),
			innerDiv = div.children()[ 0 ];

		$( "body" ).append( div );
		w1 = innerDiv.offsetWidth;
		div.css( "overflow", "scroll" );

		w2 = innerDiv.offsetWidth;

		if ( w1 === w2 ) {
			w2 = div[ 0 ].clientWidth;
		}

		div.remove();

		return ( cachedScrollbarWidth = w1 - w2 );
	},
	getScrollInfo: function( within ) {
		var overflowX = within.isWindow || within.isDocument ? "" :
				within.element.css( "overflow-x" ),
			overflowY = within.isWindow || within.isDocument ? "" :
				within.element.css( "overflow-y" ),
			hasOverflowX = overflowX === "scroll" ||
				( overflowX === "auto" && within.width < within.element[ 0 ].scrollWidth ),
			hasOverflowY = overflowY === "scroll" ||
				( overflowY === "auto" && within.height < within.element[ 0 ].scrollHeight );
		return {
			width: hasOverflowY ? $.position.scrollbarWidth() : 0,
			height: hasOverflowX ? $.position.scrollbarWidth() : 0
		};
	},
	getWithinInfo: function( element ) {
		var withinElement = $( element || window ),
			isWindow = $.isWindow( withinElement[ 0 ] ),
			isDocument = !!withinElement[ 0 ] && withinElement[ 0 ].nodeType === 9,
			hasOffset = !isWindow && !isDocument;
		return {
			element: withinElement,
			isWindow: isWindow,
			isDocument: isDocument,
			offset: hasOffset ? $( element ).offset() : { left: 0, top: 0 },
			scrollLeft: withinElement.scrollLeft(),
			scrollTop: withinElement.scrollTop(),
			width: withinElement.outerWidth(),
			height: withinElement.outerHeight()
		};
	}
};

$.fn.position = function( options ) {
	if ( !options || !options.of ) {
		return _position.apply( this, arguments );
	}

	// Make a copy, we don't want to modify arguments
	options = $.extend( {}, options );

	var atOffset, targetWidth, targetHeight, targetOffset, basePosition, dimensions,
		target = $( options.of ),
		within = $.position.getWithinInfo( options.within ),
		scrollInfo = $.position.getScrollInfo( within ),
		collision = ( options.collision || "flip" ).split( " " ),
		offsets = {};

	dimensions = getDimensions( target );
	if ( target[ 0 ].preventDefault ) {

		// Force left top to allow flipping
		options.at = "left top";
	}
	targetWidth = dimensions.width;
	targetHeight = dimensions.height;
	targetOffset = dimensions.offset;

	// Clone to reuse original targetOffset later
	basePosition = $.extend( {}, targetOffset );

	// Force my and at to have valid horizontal and vertical positions
	// if a value is missing or invalid, it will be converted to center
	$.each( [ "my", "at" ], function() {
		var pos = ( options[ this ] || "" ).split( " " ),
			horizontalOffset,
			verticalOffset;

		if ( pos.length === 1 ) {
			pos = rhorizontal.test( pos[ 0 ] ) ?
				pos.concat( [ "center" ] ) :
				rvertical.test( pos[ 0 ] ) ?
					[ "center" ].concat( pos ) :
					[ "center", "center" ];
		}
		pos[ 0 ] = rhorizontal.test( pos[ 0 ] ) ? pos[ 0 ] : "center";
		pos[ 1 ] = rvertical.test( pos[ 1 ] ) ? pos[ 1 ] : "center";

		// Calculate offsets
		horizontalOffset = roffset.exec( pos[ 0 ] );
		verticalOffset = roffset.exec( pos[ 1 ] );
		offsets[ this ] = [
			horizontalOffset ? horizontalOffset[ 0 ] : 0,
			verticalOffset ? verticalOffset[ 0 ] : 0
		];

		// Reduce to just the positions without the offsets
		options[ this ] = [
			rposition.exec( pos[ 0 ] )[ 0 ],
			rposition.exec( pos[ 1 ] )[ 0 ]
		];
	} );

	// Normalize collision option
	if ( collision.length === 1 ) {
		collision[ 1 ] = collision[ 0 ];
	}

	if ( options.at[ 0 ] === "right" ) {
		basePosition.left += targetWidth;
	} else if ( options.at[ 0 ] === "center" ) {
		basePosition.left += targetWidth / 2;
	}

	if ( options.at[ 1 ] === "bottom" ) {
		basePosition.top += targetHeight;
	} else if ( options.at[ 1 ] === "center" ) {
		basePosition.top += targetHeight / 2;
	}

	atOffset = getOffsets( offsets.at, targetWidth, targetHeight );
	basePosition.left += atOffset[ 0 ];
	basePosition.top += atOffset[ 1 ];

	return this.each( function() {
		var collisionPosition, using,
			elem = $( this ),
			elemWidth = elem.outerWidth(),
			elemHeight = elem.outerHeight(),
			marginLeft = parseCss( this, "marginLeft" ),
			marginTop = parseCss( this, "marginTop" ),
			collisionWidth = elemWidth + marginLeft + parseCss( this, "marginRight" ) +
				scrollInfo.width,
			collisionHeight = elemHeight + marginTop + parseCss( this, "marginBottom" ) +
				scrollInfo.height,
			position = $.extend( {}, basePosition ),
			myOffset = getOffsets( offsets.my, elem.outerWidth(), elem.outerHeight() );

		if ( options.my[ 0 ] === "right" ) {
			position.left -= elemWidth;
		} else if ( options.my[ 0 ] === "center" ) {
			position.left -= elemWidth / 2;
		}

		if ( options.my[ 1 ] === "bottom" ) {
			position.top -= elemHeight;
		} else if ( options.my[ 1 ] === "center" ) {
			position.top -= elemHeight / 2;
		}

		position.left += myOffset[ 0 ];
		position.top += myOffset[ 1 ];

		collisionPosition = {
			marginLeft: marginLeft,
			marginTop: marginTop
		};

		$.each( [ "left", "top" ], function( i, dir ) {
			if ( $.ui.position[ collision[ i ] ] ) {
				$.ui.position[ collision[ i ] ][ dir ]( position, {
					targetWidth: targetWidth,
					targetHeight: targetHeight,
					elemWidth: elemWidth,
					elemHeight: elemHeight,
					collisionPosition: collisionPosition,
					collisionWidth: collisionWidth,
					collisionHeight: collisionHeight,
					offset: [ atOffset[ 0 ] + myOffset[ 0 ], atOffset [ 1 ] + myOffset[ 1 ] ],
					my: options.my,
					at: options.at,
					within: within,
					elem: elem
				} );
			}
		} );

		if ( options.using ) {

			// Adds feedback as second argument to using callback, if present
			using = function( props ) {
				var left = targetOffset.left - position.left,
					right = left + targetWidth - elemWidth,
					top = targetOffset.top - position.top,
					bottom = top + targetHeight - elemHeight,
					feedback = {
						target: {
							element: target,
							left: targetOffset.left,
							top: targetOffset.top,
							width: targetWidth,
							height: targetHeight
						},
						element: {
							element: elem,
							left: position.left,
							top: position.top,
							width: elemWidth,
							height: elemHeight
						},
						horizontal: right < 0 ? "left" : left > 0 ? "right" : "center",
						vertical: bottom < 0 ? "top" : top > 0 ? "bottom" : "middle"
					};
				if ( targetWidth < elemWidth && abs( left + right ) < targetWidth ) {
					feedback.horizontal = "center";
				}
				if ( targetHeight < elemHeight && abs( top + bottom ) < targetHeight ) {
					feedback.vertical = "middle";
				}
				if ( max( abs( left ), abs( right ) ) > max( abs( top ), abs( bottom ) ) ) {
					feedback.important = "horizontal";
				} else {
					feedback.important = "vertical";
				}
				options.using.call( this, props, feedback );
			};
		}

		elem.offset( $.extend( position, { using: using } ) );
	} );
};

$.ui.position = {
	fit: {
		left: function( position, data ) {
			var within = data.within,
				withinOffset = within.isWindow ? within.scrollLeft : within.offset.left,
				outerWidth = within.width,
				collisionPosLeft = position.left - data.collisionPosition.marginLeft,
				overLeft = withinOffset - collisionPosLeft,
				overRight = collisionPosLeft + data.collisionWidth - outerWidth - withinOffset,
				newOverRight;

			// Element is wider than within
			if ( data.collisionWidth > outerWidth ) {

				// Element is initially over the left side of within
				if ( overLeft > 0 && overRight <= 0 ) {
					newOverRight = position.left + overLeft + data.collisionWidth - outerWidth -
						withinOffset;
					position.left += overLeft - newOverRight;

				// Element is initially over right side of within
				} else if ( overRight > 0 && overLeft <= 0 ) {
					position.left = withinOffset;

				// Element is initially over both left and right sides of within
				} else {
					if ( overLeft > overRight ) {
						position.left = withinOffset + outerWidth - data.collisionWidth;
					} else {
						position.left = withinOffset;
					}
				}

			// Too far left -> align with left edge
			} else if ( overLeft > 0 ) {
				position.left += overLeft;

			// Too far right -> align with right edge
			} else if ( overRight > 0 ) {
				position.left -= overRight;

			// Adjust based on position and margin
			} else {
				position.left = max( position.left - collisionPosLeft, position.left );
			}
		},
		top: function( position, data ) {
			var within = data.within,
				withinOffset = within.isWindow ? within.scrollTop : within.offset.top,
				outerHeight = data.within.height,
				collisionPosTop = position.top - data.collisionPosition.marginTop,
				overTop = withinOffset - collisionPosTop,
				overBottom = collisionPosTop + data.collisionHeight - outerHeight - withinOffset,
				newOverBottom;

			// Element is taller than within
			if ( data.collisionHeight > outerHeight ) {

				// Element is initially over the top of within
				if ( overTop > 0 && overBottom <= 0 ) {
					newOverBottom = position.top + overTop + data.collisionHeight - outerHeight -
						withinOffset;
					position.top += overTop - newOverBottom;

				// Element is initially over bottom of within
				} else if ( overBottom > 0 && overTop <= 0 ) {
					position.top = withinOffset;

				// Element is initially over both top and bottom of within
				} else {
					if ( overTop > overBottom ) {
						position.top = withinOffset + outerHeight - data.collisionHeight;
					} else {
						position.top = withinOffset;
					}
				}

			// Too far up -> align with top
			} else if ( overTop > 0 ) {
				position.top += overTop;

			// Too far down -> align with bottom edge
			} else if ( overBottom > 0 ) {
				position.top -= overBottom;

			// Adjust based on position and margin
			} else {
				position.top = max( position.top - collisionPosTop, position.top );
			}
		}
	},
	flip: {
		left: function( position, data ) {
			var within = data.within,
				withinOffset = within.offset.left + within.scrollLeft,
				outerWidth = within.width,
				offsetLeft = within.isWindow ? within.scrollLeft : within.offset.left,
				collisionPosLeft = position.left - data.collisionPosition.marginLeft,
				overLeft = collisionPosLeft - offsetLeft,
				overRight = collisionPosLeft + data.collisionWidth - outerWidth - offsetLeft,
				myOffset = data.my[ 0 ] === "left" ?
					-data.elemWidth :
					data.my[ 0 ] === "right" ?
						data.elemWidth :
						0,
				atOffset = data.at[ 0 ] === "left" ?
					data.targetWidth :
					data.at[ 0 ] === "right" ?
						-data.targetWidth :
						0,
				offset = -2 * data.offset[ 0 ],
				newOverRight,
				newOverLeft;

			if ( overLeft < 0 ) {
				newOverRight = position.left + myOffset + atOffset + offset + data.collisionWidth -
					outerWidth - withinOffset;
				if ( newOverRight < 0 || newOverRight < abs( overLeft ) ) {
					position.left += myOffset + atOffset + offset;
				}
			} else if ( overRight > 0 ) {
				newOverLeft = position.left - data.collisionPosition.marginLeft + myOffset +
					atOffset + offset - offsetLeft;
				if ( newOverLeft > 0 || abs( newOverLeft ) < overRight ) {
					position.left += myOffset + atOffset + offset;
				}
			}
		},
		top: function( position, data ) {
			var within = data.within,
				withinOffset = within.offset.top + within.scrollTop,
				outerHeight = within.height,
				offsetTop = within.isWindow ? within.scrollTop : within.offset.top,
				collisionPosTop = position.top - data.collisionPosition.marginTop,
				overTop = collisionPosTop - offsetTop,
				overBottom = collisionPosTop + data.collisionHeight - outerHeight - offsetTop,
				top = data.my[ 1 ] === "top",
				myOffset = top ?
					-data.elemHeight :
					data.my[ 1 ] === "bottom" ?
						data.elemHeight :
						0,
				atOffset = data.at[ 1 ] === "top" ?
					data.targetHeight :
					data.at[ 1 ] === "bottom" ?
						-data.targetHeight :
						0,
				offset = -2 * data.offset[ 1 ],
				newOverTop,
				newOverBottom;
			if ( overTop < 0 ) {
				newOverBottom = position.top + myOffset + atOffset + offset + data.collisionHeight -
					outerHeight - withinOffset;
				if ( newOverBottom < 0 || newOverBottom < abs( overTop ) ) {
					position.top += myOffset + atOffset + offset;
				}
			} else if ( overBottom > 0 ) {
				newOverTop = position.top - data.collisionPosition.marginTop + myOffset + atOffset +
					offset - offsetTop;
				if ( newOverTop > 0 || abs( newOverTop ) < overBottom ) {
					position.top += myOffset + atOffset + offset;
				}
			}
		}
	},
	flipfit: {
		left: function() {
			$.ui.position.flip.left.apply( this, arguments );
			$.ui.position.fit.left.apply( this, arguments );
		},
		top: function() {
			$.ui.position.flip.top.apply( this, arguments );
			$.ui.position.fit.top.apply( this, arguments );
		}
	}
};

} )();

var position = $.ui.position;


/*!
 * jQuery UI :data 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: :data Selector
//>>group: Core
//>>description: Selects elements which have data stored under the specified key.
//>>docs: http://api.jqueryui.com/data-selector/


var data = $.extend( $.expr[ ":" ], {
	data: $.expr.createPseudo ?
		$.expr.createPseudo( function( dataName ) {
			return function( elem ) {
				return !!$.data( elem, dataName );
			};
		} ) :

		// Support: jQuery <1.8
		function( elem, i, match ) {
			return !!$.data( elem, match[ 3 ] );
		}
} );

/*!
 * jQuery UI Disable Selection 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: disableSelection
//>>group: Core
//>>description: Disable selection of text content within the set of matched elements.
//>>docs: http://api.jqueryui.com/disableSelection/

// This file is deprecated


var disableSelection = $.fn.extend( {
	disableSelection: ( function() {
		var eventType = "onselectstart" in document.createElement( "div" ) ?
			"selectstart" :
			"mousedown";

		return function() {
			return this.on( eventType + ".ui-disableSelection", function( event ) {
				event.preventDefault();
			} );
		};
	} )(),

	enableSelection: function() {
		return this.off( ".ui-disableSelection" );
	}
} );


/*!
 * jQuery UI Effects 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Effects Core
//>>group: Effects
// jscs:disable maximumLineLength
//>>description: Extends the internal jQuery effects. Includes morphing and easing. Required by all other effects.
// jscs:enable maximumLineLength
//>>docs: http://api.jqueryui.com/category/effects-core/
//>>demos: http://jqueryui.com/effect/



var dataSpace = "ui-effects-",
	dataSpaceStyle = "ui-effects-style",
	dataSpaceAnimated = "ui-effects-animated",

	// Create a local jQuery because jQuery Color relies on it and the
	// global may not exist with AMD and a custom build (#10199)
	jQuery = $;

$.effects = {
	effect: {}
};

/*!
 * jQuery Color Animations v2.1.2
 * https://github.com/jquery/jquery-color
 *
 * Copyright 2014 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 * Date: Wed Jan 16 08:47:09 2013 -0600
 */
( function( jQuery, undefined ) {

	var stepHooks = "backgroundColor borderBottomColor borderLeftColor borderRightColor " +
		"borderTopColor color columnRuleColor outlineColor textDecorationColor textEmphasisColor",

	// Plusequals test for += 100 -= 100
	rplusequals = /^([\-+])=\s*(\d+\.?\d*)/,

	// A set of RE's that can match strings and generate color tuples.
	stringParsers = [ {
			re: /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(\d?(?:\.\d+)?)\s*)?\)/,
			parse: function( execResult ) {
				return [
					execResult[ 1 ],
					execResult[ 2 ],
					execResult[ 3 ],
					execResult[ 4 ]
				];
			}
		}, {
			re: /rgba?\(\s*(\d+(?:\.\d+)?)\%\s*,\s*(\d+(?:\.\d+)?)\%\s*,\s*(\d+(?:\.\d+)?)\%\s*(?:,\s*(\d?(?:\.\d+)?)\s*)?\)/,
			parse: function( execResult ) {
				return [
					execResult[ 1 ] * 2.55,
					execResult[ 2 ] * 2.55,
					execResult[ 3 ] * 2.55,
					execResult[ 4 ]
				];
			}
		}, {

			// This regex ignores A-F because it's compared against an already lowercased string
			re: /#([a-f0-9]{2})([a-f0-9]{2})([a-f0-9]{2})/,
			parse: function( execResult ) {
				return [
					parseInt( execResult[ 1 ], 16 ),
					parseInt( execResult[ 2 ], 16 ),
					parseInt( execResult[ 3 ], 16 )
				];
			}
		}, {

			// This regex ignores A-F because it's compared against an already lowercased string
			re: /#([a-f0-9])([a-f0-9])([a-f0-9])/,
			parse: function( execResult ) {
				return [
					parseInt( execResult[ 1 ] + execResult[ 1 ], 16 ),
					parseInt( execResult[ 2 ] + execResult[ 2 ], 16 ),
					parseInt( execResult[ 3 ] + execResult[ 3 ], 16 )
				];
			}
		}, {
			re: /hsla?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\%\s*,\s*(\d+(?:\.\d+)?)\%\s*(?:,\s*(\d?(?:\.\d+)?)\s*)?\)/,
			space: "hsla",
			parse: function( execResult ) {
				return [
					execResult[ 1 ],
					execResult[ 2 ] / 100,
					execResult[ 3 ] / 100,
					execResult[ 4 ]
				];
			}
		} ],

	// JQuery.Color( )
	color = jQuery.Color = function( color, green, blue, alpha ) {
		return new jQuery.Color.fn.parse( color, green, blue, alpha );
	},
	spaces = {
		rgba: {
			props: {
				red: {
					idx: 0,
					type: "byte"
				},
				green: {
					idx: 1,
					type: "byte"
				},
				blue: {
					idx: 2,
					type: "byte"
				}
			}
		},

		hsla: {
			props: {
				hue: {
					idx: 0,
					type: "degrees"
				},
				saturation: {
					idx: 1,
					type: "percent"
				},
				lightness: {
					idx: 2,
					type: "percent"
				}
			}
		}
	},
	propTypes = {
		"byte": {
			floor: true,
			max: 255
		},
		"percent": {
			max: 1
		},
		"degrees": {
			mod: 360,
			floor: true
		}
	},
	support = color.support = {},

	// Element for support tests
	supportElem = jQuery( "<p>" )[ 0 ],

	// Colors = jQuery.Color.names
	colors,

	// Local aliases of functions called often
	each = jQuery.each;

// Determine rgba support immediately
supportElem.style.cssText = "background-color:rgba(1,1,1,.5)";
support.rgba = supportElem.style.backgroundColor.indexOf( "rgba" ) > -1;

// Define cache name and alpha properties
// for rgba and hsla spaces
each( spaces, function( spaceName, space ) {
	space.cache = "_" + spaceName;
	space.props.alpha = {
		idx: 3,
		type: "percent",
		def: 1
	};
} );

function clamp( value, prop, allowEmpty ) {
	var type = propTypes[ prop.type ] || {};

	if ( value == null ) {
		return ( allowEmpty || !prop.def ) ? null : prop.def;
	}

	// ~~ is an short way of doing floor for positive numbers
	value = type.floor ? ~~value : parseFloat( value );

	// IE will pass in empty strings as value for alpha,
	// which will hit this case
	if ( isNaN( value ) ) {
		return prop.def;
	}

	if ( type.mod ) {

		// We add mod before modding to make sure that negatives values
		// get converted properly: -10 -> 350
		return ( value + type.mod ) % type.mod;
	}

	// For now all property types without mod have min and max
	return 0 > value ? 0 : type.max < value ? type.max : value;
}

function stringParse( string ) {
	var inst = color(),
		rgba = inst._rgba = [];

	string = string.toLowerCase();

	each( stringParsers, function( i, parser ) {
		var parsed,
			match = parser.re.exec( string ),
			values = match && parser.parse( match ),
			spaceName = parser.space || "rgba";

		if ( values ) {
			parsed = inst[ spaceName ]( values );

			// If this was an rgba parse the assignment might happen twice
			// oh well....
			inst[ spaces[ spaceName ].cache ] = parsed[ spaces[ spaceName ].cache ];
			rgba = inst._rgba = parsed._rgba;

			// Exit each( stringParsers ) here because we matched
			return false;
		}
	} );

	// Found a stringParser that handled it
	if ( rgba.length ) {

		// If this came from a parsed string, force "transparent" when alpha is 0
		// chrome, (and maybe others) return "transparent" as rgba(0,0,0,0)
		if ( rgba.join() === "0,0,0,0" ) {
			jQuery.extend( rgba, colors.transparent );
		}
		return inst;
	}

	// Named colors
	return colors[ string ];
}

color.fn = jQuery.extend( color.prototype, {
	parse: function( red, green, blue, alpha ) {
		if ( red === undefined ) {
			this._rgba = [ null, null, null, null ];
			return this;
		}
		if ( red.jquery || red.nodeType ) {
			red = jQuery( red ).css( green );
			green = undefined;
		}

		var inst = this,
			type = jQuery.type( red ),
			rgba = this._rgba = [];

		// More than 1 argument specified - assume ( red, green, blue, alpha )
		if ( green !== undefined ) {
			red = [ red, green, blue, alpha ];
			type = "array";
		}

		if ( type === "string" ) {
			return this.parse( stringParse( red ) || colors._default );
		}

		if ( type === "array" ) {
			each( spaces.rgba.props, function( key, prop ) {
				rgba[ prop.idx ] = clamp( red[ prop.idx ], prop );
			} );
			return this;
		}

		if ( type === "object" ) {
			if ( red instanceof color ) {
				each( spaces, function( spaceName, space ) {
					if ( red[ space.cache ] ) {
						inst[ space.cache ] = red[ space.cache ].slice();
					}
				} );
			} else {
				each( spaces, function( spaceName, space ) {
					var cache = space.cache;
					each( space.props, function( key, prop ) {

						// If the cache doesn't exist, and we know how to convert
						if ( !inst[ cache ] && space.to ) {

							// If the value was null, we don't need to copy it
							// if the key was alpha, we don't need to copy it either
							if ( key === "alpha" || red[ key ] == null ) {
								return;
							}
							inst[ cache ] = space.to( inst._rgba );
						}

						// This is the only case where we allow nulls for ALL properties.
						// call clamp with alwaysAllowEmpty
						inst[ cache ][ prop.idx ] = clamp( red[ key ], prop, true );
					} );

					// Everything defined but alpha?
					if ( inst[ cache ] &&
							jQuery.inArray( null, inst[ cache ].slice( 0, 3 ) ) < 0 ) {

						// Use the default of 1
						inst[ cache ][ 3 ] = 1;
						if ( space.from ) {
							inst._rgba = space.from( inst[ cache ] );
						}
					}
				} );
			}
			return this;
		}
	},
	is: function( compare ) {
		var is = color( compare ),
			same = true,
			inst = this;

		each( spaces, function( _, space ) {
			var localCache,
				isCache = is[ space.cache ];
			if ( isCache ) {
				localCache = inst[ space.cache ] || space.to && space.to( inst._rgba ) || [];
				each( space.props, function( _, prop ) {
					if ( isCache[ prop.idx ] != null ) {
						same = ( isCache[ prop.idx ] === localCache[ prop.idx ] );
						return same;
					}
				} );
			}
			return same;
		} );
		return same;
	},
	_space: function() {
		var used = [],
			inst = this;
		each( spaces, function( spaceName, space ) {
			if ( inst[ space.cache ] ) {
				used.push( spaceName );
			}
		} );
		return used.pop();
	},
	transition: function( other, distance ) {
		var end = color( other ),
			spaceName = end._space(),
			space = spaces[ spaceName ],
			startColor = this.alpha() === 0 ? color( "transparent" ) : this,
			start = startColor[ space.cache ] || space.to( startColor._rgba ),
			result = start.slice();

		end = end[ space.cache ];
		each( space.props, function( key, prop ) {
			var index = prop.idx,
				startValue = start[ index ],
				endValue = end[ index ],
				type = propTypes[ prop.type ] || {};

			// If null, don't override start value
			if ( endValue === null ) {
				return;
			}

			// If null - use end
			if ( startValue === null ) {
				result[ index ] = endValue;
			} else {
				if ( type.mod ) {
					if ( endValue - startValue > type.mod / 2 ) {
						startValue += type.mod;
					} else if ( startValue - endValue > type.mod / 2 ) {
						startValue -= type.mod;
					}
				}
				result[ index ] = clamp( ( endValue - startValue ) * distance + startValue, prop );
			}
		} );
		return this[ spaceName ]( result );
	},
	blend: function( opaque ) {

		// If we are already opaque - return ourself
		if ( this._rgba[ 3 ] === 1 ) {
			return this;
		}

		var rgb = this._rgba.slice(),
			a = rgb.pop(),
			blend = color( opaque )._rgba;

		return color( jQuery.map( rgb, function( v, i ) {
			return ( 1 - a ) * blend[ i ] + a * v;
		} ) );
	},
	toRgbaString: function() {
		var prefix = "rgba(",
			rgba = jQuery.map( this._rgba, function( v, i ) {
				return v == null ? ( i > 2 ? 1 : 0 ) : v;
			} );

		if ( rgba[ 3 ] === 1 ) {
			rgba.pop();
			prefix = "rgb(";
		}

		return prefix + rgba.join() + ")";
	},
	toHslaString: function() {
		var prefix = "hsla(",
			hsla = jQuery.map( this.hsla(), function( v, i ) {
				if ( v == null ) {
					v = i > 2 ? 1 : 0;
				}

				// Catch 1 and 2
				if ( i && i < 3 ) {
					v = Math.round( v * 100 ) + "%";
				}
				return v;
			} );

		if ( hsla[ 3 ] === 1 ) {
			hsla.pop();
			prefix = "hsl(";
		}
		return prefix + hsla.join() + ")";
	},
	toHexString: function( includeAlpha ) {
		var rgba = this._rgba.slice(),
			alpha = rgba.pop();

		if ( includeAlpha ) {
			rgba.push( ~~( alpha * 255 ) );
		}

		return "#" + jQuery.map( rgba, function( v ) {

			// Default to 0 when nulls exist
			v = ( v || 0 ).toString( 16 );
			return v.length === 1 ? "0" + v : v;
		} ).join( "" );
	},
	toString: function() {
		return this._rgba[ 3 ] === 0 ? "transparent" : this.toRgbaString();
	}
} );
color.fn.parse.prototype = color.fn;

// Hsla conversions adapted from:
// https://code.google.com/p/maashaack/source/browse/packages/graphics/trunk/src/graphics/colors/HUE2RGB.as?r=5021

function hue2rgb( p, q, h ) {
	h = ( h + 1 ) % 1;
	if ( h * 6 < 1 ) {
		return p + ( q - p ) * h * 6;
	}
	if ( h * 2 < 1 ) {
		return q;
	}
	if ( h * 3 < 2 ) {
		return p + ( q - p ) * ( ( 2 / 3 ) - h ) * 6;
	}
	return p;
}

spaces.hsla.to = function( rgba ) {
	if ( rgba[ 0 ] == null || rgba[ 1 ] == null || rgba[ 2 ] == null ) {
		return [ null, null, null, rgba[ 3 ] ];
	}
	var r = rgba[ 0 ] / 255,
		g = rgba[ 1 ] / 255,
		b = rgba[ 2 ] / 255,
		a = rgba[ 3 ],
		max = Math.max( r, g, b ),
		min = Math.min( r, g, b ),
		diff = max - min,
		add = max + min,
		l = add * 0.5,
		h, s;

	if ( min === max ) {
		h = 0;
	} else if ( r === max ) {
		h = ( 60 * ( g - b ) / diff ) + 360;
	} else if ( g === max ) {
		h = ( 60 * ( b - r ) / diff ) + 120;
	} else {
		h = ( 60 * ( r - g ) / diff ) + 240;
	}

	// Chroma (diff) == 0 means greyscale which, by definition, saturation = 0%
	// otherwise, saturation is based on the ratio of chroma (diff) to lightness (add)
	if ( diff === 0 ) {
		s = 0;
	} else if ( l <= 0.5 ) {
		s = diff / add;
	} else {
		s = diff / ( 2 - add );
	}
	return [ Math.round( h ) % 360, s, l, a == null ? 1 : a ];
};

spaces.hsla.from = function( hsla ) {
	if ( hsla[ 0 ] == null || hsla[ 1 ] == null || hsla[ 2 ] == null ) {
		return [ null, null, null, hsla[ 3 ] ];
	}
	var h = hsla[ 0 ] / 360,
		s = hsla[ 1 ],
		l = hsla[ 2 ],
		a = hsla[ 3 ],
		q = l <= 0.5 ? l * ( 1 + s ) : l + s - l * s,
		p = 2 * l - q;

	return [
		Math.round( hue2rgb( p, q, h + ( 1 / 3 ) ) * 255 ),
		Math.round( hue2rgb( p, q, h ) * 255 ),
		Math.round( hue2rgb( p, q, h - ( 1 / 3 ) ) * 255 ),
		a
	];
};

each( spaces, function( spaceName, space ) {
	var props = space.props,
		cache = space.cache,
		to = space.to,
		from = space.from;

	// Makes rgba() and hsla()
	color.fn[ spaceName ] = function( value ) {

		// Generate a cache for this space if it doesn't exist
		if ( to && !this[ cache ] ) {
			this[ cache ] = to( this._rgba );
		}
		if ( value === undefined ) {
			return this[ cache ].slice();
		}

		var ret,
			type = jQuery.type( value ),
			arr = ( type === "array" || type === "object" ) ? value : arguments,
			local = this[ cache ].slice();

		each( props, function( key, prop ) {
			var val = arr[ type === "object" ? key : prop.idx ];
			if ( val == null ) {
				val = local[ prop.idx ];
			}
			local[ prop.idx ] = clamp( val, prop );
		} );

		if ( from ) {
			ret = color( from( local ) );
			ret[ cache ] = local;
			return ret;
		} else {
			return color( local );
		}
	};

	// Makes red() green() blue() alpha() hue() saturation() lightness()
	each( props, function( key, prop ) {

		// Alpha is included in more than one space
		if ( color.fn[ key ] ) {
			return;
		}
		color.fn[ key ] = function( value ) {
			var vtype = jQuery.type( value ),
				fn = ( key === "alpha" ? ( this._hsla ? "hsla" : "rgba" ) : spaceName ),
				local = this[ fn ](),
				cur = local[ prop.idx ],
				match;

			if ( vtype === "undefined" ) {
				return cur;
			}

			if ( vtype === "function" ) {
				value = value.call( this, cur );
				vtype = jQuery.type( value );
			}
			if ( value == null && prop.empty ) {
				return this;
			}
			if ( vtype === "string" ) {
				match = rplusequals.exec( value );
				if ( match ) {
					value = cur + parseFloat( match[ 2 ] ) * ( match[ 1 ] === "+" ? 1 : -1 );
				}
			}
			local[ prop.idx ] = value;
			return this[ fn ]( local );
		};
	} );
} );

// Add cssHook and .fx.step function for each named hook.
// accept a space separated string of properties
color.hook = function( hook ) {
	var hooks = hook.split( " " );
	each( hooks, function( i, hook ) {
		jQuery.cssHooks[ hook ] = {
			set: function( elem, value ) {
				var parsed, curElem,
					backgroundColor = "";

				if ( value !== "transparent" && ( jQuery.type( value ) !== "string" ||
						( parsed = stringParse( value ) ) ) ) {
					value = color( parsed || value );
					if ( !support.rgba && value._rgba[ 3 ] !== 1 ) {
						curElem = hook === "backgroundColor" ? elem.parentNode : elem;
						while (
							( backgroundColor === "" || backgroundColor === "transparent" ) &&
							curElem && curElem.style
						) {
							try {
								backgroundColor = jQuery.css( curElem, "backgroundColor" );
								curElem = curElem.parentNode;
							} catch ( e ) {
							}
						}

						value = value.blend( backgroundColor && backgroundColor !== "transparent" ?
							backgroundColor :
							"_default" );
					}

					value = value.toRgbaString();
				}
				try {
					elem.style[ hook ] = value;
				} catch ( e ) {

					// Wrapped to prevent IE from throwing errors on "invalid" values like
					// 'auto' or 'inherit'
				}
			}
		};
		jQuery.fx.step[ hook ] = function( fx ) {
			if ( !fx.colorInit ) {
				fx.start = color( fx.elem, hook );
				fx.end = color( fx.end );
				fx.colorInit = true;
			}
			jQuery.cssHooks[ hook ].set( fx.elem, fx.start.transition( fx.end, fx.pos ) );
		};
	} );

};

color.hook( stepHooks );

jQuery.cssHooks.borderColor = {
	expand: function( value ) {
		var expanded = {};

		each( [ "Top", "Right", "Bottom", "Left" ], function( i, part ) {
			expanded[ "border" + part + "Color" ] = value;
		} );
		return expanded;
	}
};

// Basic color names only.
// Usage of any of the other color names requires adding yourself or including
// jquery.color.svg-names.js.
colors = jQuery.Color.names = {

	// 4.1. Basic color keywords
	aqua: "#00ffff",
	black: "#000000",
	blue: "#0000ff",
	fuchsia: "#ff00ff",
	gray: "#808080",
	green: "#008000",
	lime: "#00ff00",
	maroon: "#800000",
	navy: "#000080",
	olive: "#808000",
	purple: "#800080",
	red: "#ff0000",
	silver: "#c0c0c0",
	teal: "#008080",
	white: "#ffffff",
	yellow: "#ffff00",

	// 4.2.3. "transparent" color keyword
	transparent: [ null, null, null, 0 ],

	_default: "#ffffff"
};

} )( jQuery );

/******************************************************************************/
/****************************** CLASS ANIMATIONS ******************************/
/******************************************************************************/
( function() {

var classAnimationActions = [ "add", "remove", "toggle" ],
	shorthandStyles = {
		border: 1,
		borderBottom: 1,
		borderColor: 1,
		borderLeft: 1,
		borderRight: 1,
		borderTop: 1,
		borderWidth: 1,
		margin: 1,
		padding: 1
	};

$.each(
	[ "borderLeftStyle", "borderRightStyle", "borderBottomStyle", "borderTopStyle" ],
	function( _, prop ) {
		$.fx.step[ prop ] = function( fx ) {
			if ( fx.end !== "none" && !fx.setAttr || fx.pos === 1 && !fx.setAttr ) {
				jQuery.style( fx.elem, prop, fx.end );
				fx.setAttr = true;
			}
		};
	}
);

function getElementStyles( elem ) {
	var key, len,
		style = elem.ownerDocument.defaultView ?
			elem.ownerDocument.defaultView.getComputedStyle( elem, null ) :
			elem.currentStyle,
		styles = {};

	if ( style && style.length && style[ 0 ] && style[ style[ 0 ] ] ) {
		len = style.length;
		while ( len-- ) {
			key = style[ len ];
			if ( typeof style[ key ] === "string" ) {
				styles[ $.camelCase( key ) ] = style[ key ];
			}
		}

	// Support: Opera, IE <9
	} else {
		for ( key in style ) {
			if ( typeof style[ key ] === "string" ) {
				styles[ key ] = style[ key ];
			}
		}
	}

	return styles;
}

function styleDifference( oldStyle, newStyle ) {
	var diff = {},
		name, value;

	for ( name in newStyle ) {
		value = newStyle[ name ];
		if ( oldStyle[ name ] !== value ) {
			if ( !shorthandStyles[ name ] ) {
				if ( $.fx.step[ name ] || !isNaN( parseFloat( value ) ) ) {
					diff[ name ] = value;
				}
			}
		}
	}

	return diff;
}

// Support: jQuery <1.8
if ( !$.fn.addBack ) {
	$.fn.addBack = function( selector ) {
		return this.add( selector == null ?
			this.prevObject : this.prevObject.filter( selector )
		);
	};
}

$.effects.animateClass = function( value, duration, easing, callback ) {
	var o = $.speed( duration, easing, callback );

	return this.queue( function() {
		var animated = $( this ),
			baseClass = animated.attr( "class" ) || "",
			applyClassChange,
			allAnimations = o.children ? animated.find( "*" ).addBack() : animated;

		// Map the animated objects to store the original styles.
		allAnimations = allAnimations.map( function() {
			var el = $( this );
			return {
				el: el,
				start: getElementStyles( this )
			};
		} );

		// Apply class change
		applyClassChange = function() {
			$.each( classAnimationActions, function( i, action ) {
				if ( value[ action ] ) {
					animated[ action + "Class" ]( value[ action ] );
				}
			} );
		};
		applyClassChange();

		// Map all animated objects again - calculate new styles and diff
		allAnimations = allAnimations.map( function() {
			this.end = getElementStyles( this.el[ 0 ] );
			this.diff = styleDifference( this.start, this.end );
			return this;
		} );

		// Apply original class
		animated.attr( "class", baseClass );

		// Map all animated objects again - this time collecting a promise
		allAnimations = allAnimations.map( function() {
			var styleInfo = this,
				dfd = $.Deferred(),
				opts = $.extend( {}, o, {
					queue: false,
					complete: function() {
						dfd.resolve( styleInfo );
					}
				} );

			this.el.animate( this.diff, opts );
			return dfd.promise();
		} );

		// Once all animations have completed:
		$.when.apply( $, allAnimations.get() ).done( function() {

			// Set the final class
			applyClassChange();

			// For each animated element,
			// clear all css properties that were animated
			$.each( arguments, function() {
				var el = this.el;
				$.each( this.diff, function( key ) {
					el.css( key, "" );
				} );
			} );

			// This is guarnteed to be there if you use jQuery.speed()
			// it also handles dequeuing the next anim...
			o.complete.call( animated[ 0 ] );
		} );
	} );
};

$.fn.extend( {
	addClass: ( function( orig ) {
		return function( classNames, speed, easing, callback ) {
			return speed ?
				$.effects.animateClass.call( this,
					{ add: classNames }, speed, easing, callback ) :
				orig.apply( this, arguments );
		};
	} )( $.fn.addClass ),

	removeClass: ( function( orig ) {
		return function( classNames, speed, easing, callback ) {
			return arguments.length > 1 ?
				$.effects.animateClass.call( this,
					{ remove: classNames }, speed, easing, callback ) :
				orig.apply( this, arguments );
		};
	} )( $.fn.removeClass ),

	toggleClass: ( function( orig ) {
		return function( classNames, force, speed, easing, callback ) {
			if ( typeof force === "boolean" || force === undefined ) {
				if ( !speed ) {

					// Without speed parameter
					return orig.apply( this, arguments );
				} else {
					return $.effects.animateClass.call( this,
						( force ? { add: classNames } : { remove: classNames } ),
						speed, easing, callback );
				}
			} else {

				// Without force parameter
				return $.effects.animateClass.call( this,
					{ toggle: classNames }, force, speed, easing );
			}
		};
	} )( $.fn.toggleClass ),

	switchClass: function( remove, add, speed, easing, callback ) {
		return $.effects.animateClass.call( this, {
			add: add,
			remove: remove
		}, speed, easing, callback );
	}
} );

} )();

/******************************************************************************/
/*********************************** EFFECTS **********************************/
/******************************************************************************/

( function() {

if ( $.expr && $.expr.filters && $.expr.filters.animated ) {
	$.expr.filters.animated = ( function( orig ) {
		return function( elem ) {
			return !!$( elem ).data( dataSpaceAnimated ) || orig( elem );
		};
	} )( $.expr.filters.animated );
}

if ( $.uiBackCompat !== false ) {
	$.extend( $.effects, {

		// Saves a set of properties in a data storage
		save: function( element, set ) {
			var i = 0, length = set.length;
			for ( ; i < length; i++ ) {
				if ( set[ i ] !== null ) {
					element.data( dataSpace + set[ i ], element[ 0 ].style[ set[ i ] ] );
				}
			}
		},

		// Restores a set of previously saved properties from a data storage
		restore: function( element, set ) {
			var val, i = 0, length = set.length;
			for ( ; i < length; i++ ) {
				if ( set[ i ] !== null ) {
					val = element.data( dataSpace + set[ i ] );
					element.css( set[ i ], val );
				}
			}
		},

		setMode: function( el, mode ) {
			if ( mode === "toggle" ) {
				mode = el.is( ":hidden" ) ? "show" : "hide";
			}
			return mode;
		},

		// Wraps the element around a wrapper that copies position properties
		createWrapper: function( element ) {

			// If the element is already wrapped, return it
			if ( element.parent().is( ".ui-effects-wrapper" ) ) {
				return element.parent();
			}

			// Wrap the element
			var props = {
					width: element.outerWidth( true ),
					height: element.outerHeight( true ),
					"float": element.css( "float" )
				},
				wrapper = $( "<div></div>" )
					.addClass( "ui-effects-wrapper" )
					.css( {
						fontSize: "100%",
						background: "transparent",
						border: "none",
						margin: 0,
						padding: 0
					} ),

				// Store the size in case width/height are defined in % - Fixes #5245
				size = {
					width: element.width(),
					height: element.height()
				},
				active = document.activeElement;

			// Support: Firefox
			// Firefox incorrectly exposes anonymous content
			// https://bugzilla.mozilla.org/show_bug.cgi?id=561664
			try {
				active.id;
			} catch ( e ) {
				active = document.body;
			}

			element.wrap( wrapper );

			// Fixes #7595 - Elements lose focus when wrapped.
			if ( element[ 0 ] === active || $.contains( element[ 0 ], active ) ) {
				$( active ).trigger( "focus" );
			}

			// Hotfix for jQuery 1.4 since some change in wrap() seems to actually
			// lose the reference to the wrapped element
			wrapper = element.parent();

			// Transfer positioning properties to the wrapper
			if ( element.css( "position" ) === "static" ) {
				wrapper.css( { position: "relative" } );
				element.css( { position: "relative" } );
			} else {
				$.extend( props, {
					position: element.css( "position" ),
					zIndex: element.css( "z-index" )
				} );
				$.each( [ "top", "left", "bottom", "right" ], function( i, pos ) {
					props[ pos ] = element.css( pos );
					if ( isNaN( parseInt( props[ pos ], 10 ) ) ) {
						props[ pos ] = "auto";
					}
				} );
				element.css( {
					position: "relative",
					top: 0,
					left: 0,
					right: "auto",
					bottom: "auto"
				} );
			}
			element.css( size );

			return wrapper.css( props ).show();
		},

		removeWrapper: function( element ) {
			var active = document.activeElement;

			if ( element.parent().is( ".ui-effects-wrapper" ) ) {
				element.parent().replaceWith( element );

				// Fixes #7595 - Elements lose focus when wrapped.
				if ( element[ 0 ] === active || $.contains( element[ 0 ], active ) ) {
					$( active ).trigger( "focus" );
				}
			}

			return element;
		}
	} );
}

$.extend( $.effects, {
	version: "1.12.1",

	define: function( name, mode, effect ) {
		if ( !effect ) {
			effect = mode;
			mode = "effect";
		}

		$.effects.effect[ name ] = effect;
		$.effects.effect[ name ].mode = mode;

		return effect;
	},

	scaledDimensions: function( element, percent, direction ) {
		if ( percent === 0 ) {
			return {
				height: 0,
				width: 0,
				outerHeight: 0,
				outerWidth: 0
			};
		}

		var x = direction !== "horizontal" ? ( ( percent || 100 ) / 100 ) : 1,
			y = direction !== "vertical" ? ( ( percent || 100 ) / 100 ) : 1;

		return {
			height: element.height() * y,
			width: element.width() * x,
			outerHeight: element.outerHeight() * y,
			outerWidth: element.outerWidth() * x
		};

	},

	clipToBox: function( animation ) {
		return {
			width: animation.clip.right - animation.clip.left,
			height: animation.clip.bottom - animation.clip.top,
			left: animation.clip.left,
			top: animation.clip.top
		};
	},

	// Injects recently queued functions to be first in line (after "inprogress")
	unshift: function( element, queueLength, count ) {
		var queue = element.queue();

		if ( queueLength > 1 ) {
			queue.splice.apply( queue,
				[ 1, 0 ].concat( queue.splice( queueLength, count ) ) );
		}
		element.dequeue();
	},

	saveStyle: function( element ) {
		element.data( dataSpaceStyle, element[ 0 ].style.cssText );
	},

	restoreStyle: function( element ) {
		element[ 0 ].style.cssText = element.data( dataSpaceStyle ) || "";
		element.removeData( dataSpaceStyle );
	},

	mode: function( element, mode ) {
		var hidden = element.is( ":hidden" );

		if ( mode === "toggle" ) {
			mode = hidden ? "show" : "hide";
		}
		if ( hidden ? mode === "hide" : mode === "show" ) {
			mode = "none";
		}
		return mode;
	},

	// Translates a [top,left] array into a baseline value
	getBaseline: function( origin, original ) {
		var y, x;

		switch ( origin[ 0 ] ) {
		case "top":
			y = 0;
			break;
		case "middle":
			y = 0.5;
			break;
		case "bottom":
			y = 1;
			break;
		default:
			y = origin[ 0 ] / original.height;
		}

		switch ( origin[ 1 ] ) {
		case "left":
			x = 0;
			break;
		case "center":
			x = 0.5;
			break;
		case "right":
			x = 1;
			break;
		default:
			x = origin[ 1 ] / original.width;
		}

		return {
			x: x,
			y: y
		};
	},

	// Creates a placeholder element so that the original element can be made absolute
	createPlaceholder: function( element ) {
		var placeholder,
			cssPosition = element.css( "position" ),
			position = element.position();

		// Lock in margins first to account for form elements, which
		// will change margin if you explicitly set height
		// see: http://jsfiddle.net/JZSMt/3/ https://bugs.webkit.org/show_bug.cgi?id=107380
		// Support: Safari
		element.css( {
			marginTop: element.css( "marginTop" ),
			marginBottom: element.css( "marginBottom" ),
			marginLeft: element.css( "marginLeft" ),
			marginRight: element.css( "marginRight" )
		} )
		.outerWidth( element.outerWidth() )
		.outerHeight( element.outerHeight() );

		if ( /^(static|relative)/.test( cssPosition ) ) {
			cssPosition = "absolute";

			placeholder = $( "<" + element[ 0 ].nodeName + ">" ).insertAfter( element ).css( {

				// Convert inline to inline block to account for inline elements
				// that turn to inline block based on content (like img)
				display: /^(inline|ruby)/.test( element.css( "display" ) ) ?
					"inline-block" :
					"block",
				visibility: "hidden",

				// Margins need to be set to account for margin collapse
				marginTop: element.css( "marginTop" ),
				marginBottom: element.css( "marginBottom" ),
				marginLeft: element.css( "marginLeft" ),
				marginRight: element.css( "marginRight" ),
				"float": element.css( "float" )
			} )
			.outerWidth( element.outerWidth() )
			.outerHeight( element.outerHeight() )
			.addClass( "ui-effects-placeholder" );

			element.data( dataSpace + "placeholder", placeholder );
		}

		element.css( {
			position: cssPosition,
			left: position.left,
			top: position.top
		} );

		return placeholder;
	},

	removePlaceholder: function( element ) {
		var dataKey = dataSpace + "placeholder",
				placeholder = element.data( dataKey );

		if ( placeholder ) {
			placeholder.remove();
			element.removeData( dataKey );
		}
	},

	// Removes a placeholder if it exists and restores
	// properties that were modified during placeholder creation
	cleanUp: function( element ) {
		$.effects.restoreStyle( element );
		$.effects.removePlaceholder( element );
	},

	setTransition: function( element, list, factor, value ) {
		value = value || {};
		$.each( list, function( i, x ) {
			var unit = element.cssUnit( x );
			if ( unit[ 0 ] > 0 ) {
				value[ x ] = unit[ 0 ] * factor + unit[ 1 ];
			}
		} );
		return value;
	}
} );

// Return an effect options object for the given parameters:
function _normalizeArguments( effect, options, speed, callback ) {

	// Allow passing all options as the first parameter
	if ( $.isPlainObject( effect ) ) {
		options = effect;
		effect = effect.effect;
	}

	// Convert to an object
	effect = { effect: effect };

	// Catch (effect, null, ...)
	if ( options == null ) {
		options = {};
	}

	// Catch (effect, callback)
	if ( $.isFunction( options ) ) {
		callback = options;
		speed = null;
		options = {};
	}

	// Catch (effect, speed, ?)
	if ( typeof options === "number" || $.fx.speeds[ options ] ) {
		callback = speed;
		speed = options;
		options = {};
	}

	// Catch (effect, options, callback)
	if ( $.isFunction( speed ) ) {
		callback = speed;
		speed = null;
	}

	// Add options to effect
	if ( options ) {
		$.extend( effect, options );
	}

	speed = speed || options.duration;
	effect.duration = $.fx.off ? 0 :
		typeof speed === "number" ? speed :
		speed in $.fx.speeds ? $.fx.speeds[ speed ] :
		$.fx.speeds._default;

	effect.complete = callback || options.complete;

	return effect;
}

function standardAnimationOption( option ) {

	// Valid standard speeds (nothing, number, named speed)
	if ( !option || typeof option === "number" || $.fx.speeds[ option ] ) {
		return true;
	}

	// Invalid strings - treat as "normal" speed
	if ( typeof option === "string" && !$.effects.effect[ option ] ) {
		return true;
	}

	// Complete callback
	if ( $.isFunction( option ) ) {
		return true;
	}

	// Options hash (but not naming an effect)
	if ( typeof option === "object" && !option.effect ) {
		return true;
	}

	// Didn't match any standard API
	return false;
}

$.fn.extend( {
	effect: function( /* effect, options, speed, callback */ ) {
		var args = _normalizeArguments.apply( this, arguments ),
			effectMethod = $.effects.effect[ args.effect ],
			defaultMode = effectMethod.mode,
			queue = args.queue,
			queueName = queue || "fx",
			complete = args.complete,
			mode = args.mode,
			modes = [],
			prefilter = function( next ) {
				var el = $( this ),
					normalizedMode = $.effects.mode( el, mode ) || defaultMode;

				// Sentinel for duck-punching the :animated psuedo-selector
				el.data( dataSpaceAnimated, true );

				// Save effect mode for later use,
				// we can't just call $.effects.mode again later,
				// as the .show() below destroys the initial state
				modes.push( normalizedMode );

				// See $.uiBackCompat inside of run() for removal of defaultMode in 1.13
				if ( defaultMode && ( normalizedMode === "show" ||
						( normalizedMode === defaultMode && normalizedMode === "hide" ) ) ) {
					el.show();
				}

				if ( !defaultMode || normalizedMode !== "none" ) {
					$.effects.saveStyle( el );
				}

				if ( $.isFunction( next ) ) {
					next();
				}
			};

		if ( $.fx.off || !effectMethod ) {

			// Delegate to the original method (e.g., .show()) if possible
			if ( mode ) {
				return this[ mode ]( args.duration, complete );
			} else {
				return this.each( function() {
					if ( complete ) {
						complete.call( this );
					}
				} );
			}
		}

		function run( next ) {
			var elem = $( this );

			function cleanup() {
				elem.removeData( dataSpaceAnimated );

				$.effects.cleanUp( elem );

				if ( args.mode === "hide" ) {
					elem.hide();
				}

				done();
			}

			function done() {
				if ( $.isFunction( complete ) ) {
					complete.call( elem[ 0 ] );
				}

				if ( $.isFunction( next ) ) {
					next();
				}
			}

			// Override mode option on a per element basis,
			// as toggle can be either show or hide depending on element state
			args.mode = modes.shift();

			if ( $.uiBackCompat !== false && !defaultMode ) {
				if ( elem.is( ":hidden" ) ? mode === "hide" : mode === "show" ) {

					// Call the core method to track "olddisplay" properly
					elem[ mode ]();
					done();
				} else {
					effectMethod.call( elem[ 0 ], args, done );
				}
			} else {
				if ( args.mode === "none" ) {

					// Call the core method to track "olddisplay" properly
					elem[ mode ]();
					done();
				} else {
					effectMethod.call( elem[ 0 ], args, cleanup );
				}
			}
		}

		// Run prefilter on all elements first to ensure that
		// any showing or hiding happens before placeholder creation,
		// which ensures that any layout changes are correctly captured.
		return queue === false ?
			this.each( prefilter ).each( run ) :
			this.queue( queueName, prefilter ).queue( queueName, run );
	},

	show: ( function( orig ) {
		return function( option ) {
			if ( standardAnimationOption( option ) ) {
				return orig.apply( this, arguments );
			} else {
				var args = _normalizeArguments.apply( this, arguments );
				args.mode = "show";
				return this.effect.call( this, args );
			}
		};
	} )( $.fn.show ),

	hide: ( function( orig ) {
		return function( option ) {
			if ( standardAnimationOption( option ) ) {
				return orig.apply( this, arguments );
			} else {
				var args = _normalizeArguments.apply( this, arguments );
				args.mode = "hide";
				return this.effect.call( this, args );
			}
		};
	} )( $.fn.hide ),

	toggle: ( function( orig ) {
		return function( option ) {
			if ( standardAnimationOption( option ) || typeof option === "boolean" ) {
				return orig.apply( this, arguments );
			} else {
				var args = _normalizeArguments.apply( this, arguments );
				args.mode = "toggle";
				return this.effect.call( this, args );
			}
		};
	} )( $.fn.toggle ),

	cssUnit: function( key ) {
		var style = this.css( key ),
			val = [];

		$.each( [ "em", "px", "%", "pt" ], function( i, unit ) {
			if ( style.indexOf( unit ) > 0 ) {
				val = [ parseFloat( style ), unit ];
			}
		} );
		return val;
	},

	cssClip: function( clipObj ) {
		if ( clipObj ) {
			return this.css( "clip", "rect(" + clipObj.top + "px " + clipObj.right + "px " +
				clipObj.bottom + "px " + clipObj.left + "px)" );
		}
		return parseClip( this.css( "clip" ), this );
	},

	transfer: function( options, done ) {
		var element = $( this ),
			target = $( options.to ),
			targetFixed = target.css( "position" ) === "fixed",
			body = $( "body" ),
			fixTop = targetFixed ? body.scrollTop() : 0,
			fixLeft = targetFixed ? body.scrollLeft() : 0,
			endPosition = target.offset(),
			animation = {
				top: endPosition.top - fixTop,
				left: endPosition.left - fixLeft,
				height: target.innerHeight(),
				width: target.innerWidth()
			},
			startPosition = element.offset(),
			transfer = $( "<div class='ui-effects-transfer'></div>" )
				.appendTo( "body" )
				.addClass( options.className )
				.css( {
					top: startPosition.top - fixTop,
					left: startPosition.left - fixLeft,
					height: element.innerHeight(),
					width: element.innerWidth(),
					position: targetFixed ? "fixed" : "absolute"
				} )
				.animate( animation, options.duration, options.easing, function() {
					transfer.remove();
					if ( $.isFunction( done ) ) {
						done();
					}
				} );
	}
} );

function parseClip( str, element ) {
		var outerWidth = element.outerWidth(),
			outerHeight = element.outerHeight(),
			clipRegex = /^rect\((-?\d*\.?\d*px|-?\d+%|auto),?\s*(-?\d*\.?\d*px|-?\d+%|auto),?\s*(-?\d*\.?\d*px|-?\d+%|auto),?\s*(-?\d*\.?\d*px|-?\d+%|auto)\)$/,
			values = clipRegex.exec( str ) || [ "", 0, outerWidth, outerHeight, 0 ];

		return {
			top: parseFloat( values[ 1 ] ) || 0,
			right: values[ 2 ] === "auto" ? outerWidth : parseFloat( values[ 2 ] ),
			bottom: values[ 3 ] === "auto" ? outerHeight : parseFloat( values[ 3 ] ),
			left: parseFloat( values[ 4 ] ) || 0
		};
}

$.fx.step.clip = function( fx ) {
	if ( !fx.clipInit ) {
		fx.start = $( fx.elem ).cssClip();
		if ( typeof fx.end === "string" ) {
			fx.end = parseClip( fx.end, fx.elem );
		}
		fx.clipInit = true;
	}

	$( fx.elem ).cssClip( {
		top: fx.pos * ( fx.end.top - fx.start.top ) + fx.start.top,
		right: fx.pos * ( fx.end.right - fx.start.right ) + fx.start.right,
		bottom: fx.pos * ( fx.end.bottom - fx.start.bottom ) + fx.start.bottom,
		left: fx.pos * ( fx.end.left - fx.start.left ) + fx.start.left
	} );
};

} )();

/******************************************************************************/
/*********************************** EASING ***********************************/
/******************************************************************************/

( function() {

// Based on easing equations from Robert Penner (http://www.robertpenner.com/easing)

var baseEasings = {};

$.each( [ "Quad", "Cubic", "Quart", "Quint", "Expo" ], function( i, name ) {
	baseEasings[ name ] = function( p ) {
		return Math.pow( p, i + 2 );
	};
} );

$.extend( baseEasings, {
	Sine: function( p ) {
		return 1 - Math.cos( p * Math.PI / 2 );
	},
	Circ: function( p ) {
		return 1 - Math.sqrt( 1 - p * p );
	},
	Elastic: function( p ) {
		return p === 0 || p === 1 ? p :
			-Math.pow( 2, 8 * ( p - 1 ) ) * Math.sin( ( ( p - 1 ) * 80 - 7.5 ) * Math.PI / 15 );
	},
	Back: function( p ) {
		return p * p * ( 3 * p - 2 );
	},
	Bounce: function( p ) {
		var pow2,
			bounce = 4;

		while ( p < ( ( pow2 = Math.pow( 2, --bounce ) ) - 1 ) / 11 ) {}
		return 1 / Math.pow( 4, 3 - bounce ) - 7.5625 * Math.pow( ( pow2 * 3 - 2 ) / 22 - p, 2 );
	}
} );

$.each( baseEasings, function( name, easeIn ) {
	$.easing[ "easeIn" + name ] = easeIn;
	$.easing[ "easeOut" + name ] = function( p ) {
		return 1 - easeIn( 1 - p );
	};
	$.easing[ "easeInOut" + name ] = function( p ) {
		return p < 0.5 ?
			easeIn( p * 2 ) / 2 :
			1 - easeIn( p * -2 + 2 ) / 2;
	};
} );

} )();

var effect = $.effects;


/*!
 * jQuery UI Effects Blind 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Blind Effect
//>>group: Effects
//>>description: Blinds the element.
//>>docs: http://api.jqueryui.com/blind-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectBlind = $.effects.define( "blind", "hide", function( options, done ) {
	var map = {
			up: [ "bottom", "top" ],
			vertical: [ "bottom", "top" ],
			down: [ "top", "bottom" ],
			left: [ "right", "left" ],
			horizontal: [ "right", "left" ],
			right: [ "left", "right" ]
		},
		element = $( this ),
		direction = options.direction || "up",
		start = element.cssClip(),
		animate = { clip: $.extend( {}, start ) },
		placeholder = $.effects.createPlaceholder( element );

	animate.clip[ map[ direction ][ 0 ] ] = animate.clip[ map[ direction ][ 1 ] ];

	if ( options.mode === "show" ) {
		element.cssClip( animate.clip );
		if ( placeholder ) {
			placeholder.css( $.effects.clipToBox( animate ) );
		}

		animate.clip = start;
	}

	if ( placeholder ) {
		placeholder.animate( $.effects.clipToBox( animate ), options.duration, options.easing );
	}

	element.animate( animate, {
		queue: false,
		duration: options.duration,
		easing: options.easing,
		complete: done
	} );
} );


/*!
 * jQuery UI Effects Bounce 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Bounce Effect
//>>group: Effects
//>>description: Bounces an element horizontally or vertically n times.
//>>docs: http://api.jqueryui.com/bounce-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectBounce = $.effects.define( "bounce", function( options, done ) {
	var upAnim, downAnim, refValue,
		element = $( this ),

		// Defaults:
		mode = options.mode,
		hide = mode === "hide",
		show = mode === "show",
		direction = options.direction || "up",
		distance = options.distance,
		times = options.times || 5,

		// Number of internal animations
		anims = times * 2 + ( show || hide ? 1 : 0 ),
		speed = options.duration / anims,
		easing = options.easing,

		// Utility:
		ref = ( direction === "up" || direction === "down" ) ? "top" : "left",
		motion = ( direction === "up" || direction === "left" ),
		i = 0,

		queuelen = element.queue().length;

	$.effects.createPlaceholder( element );

	refValue = element.css( ref );

	// Default distance for the BIGGEST bounce is the outer Distance / 3
	if ( !distance ) {
		distance = element[ ref === "top" ? "outerHeight" : "outerWidth" ]() / 3;
	}

	if ( show ) {
		downAnim = { opacity: 1 };
		downAnim[ ref ] = refValue;

		// If we are showing, force opacity 0 and set the initial position
		// then do the "first" animation
		element
			.css( "opacity", 0 )
			.css( ref, motion ? -distance * 2 : distance * 2 )
			.animate( downAnim, speed, easing );
	}

	// Start at the smallest distance if we are hiding
	if ( hide ) {
		distance = distance / Math.pow( 2, times - 1 );
	}

	downAnim = {};
	downAnim[ ref ] = refValue;

	// Bounces up/down/left/right then back to 0 -- times * 2 animations happen here
	for ( ; i < times; i++ ) {
		upAnim = {};
		upAnim[ ref ] = ( motion ? "-=" : "+=" ) + distance;

		element
			.animate( upAnim, speed, easing )
			.animate( downAnim, speed, easing );

		distance = hide ? distance * 2 : distance / 2;
	}

	// Last Bounce when Hiding
	if ( hide ) {
		upAnim = { opacity: 0 };
		upAnim[ ref ] = ( motion ? "-=" : "+=" ) + distance;

		element.animate( upAnim, speed, easing );
	}

	element.queue( done );

	$.effects.unshift( element, queuelen, anims + 1 );
} );


/*!
 * jQuery UI Effects Clip 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Clip Effect
//>>group: Effects
//>>description: Clips the element on and off like an old TV.
//>>docs: http://api.jqueryui.com/clip-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectClip = $.effects.define( "clip", "hide", function( options, done ) {
	var start,
		animate = {},
		element = $( this ),
		direction = options.direction || "vertical",
		both = direction === "both",
		horizontal = both || direction === "horizontal",
		vertical = both || direction === "vertical";

	start = element.cssClip();
	animate.clip = {
		top: vertical ? ( start.bottom - start.top ) / 2 : start.top,
		right: horizontal ? ( start.right - start.left ) / 2 : start.right,
		bottom: vertical ? ( start.bottom - start.top ) / 2 : start.bottom,
		left: horizontal ? ( start.right - start.left ) / 2 : start.left
	};

	$.effects.createPlaceholder( element );

	if ( options.mode === "show" ) {
		element.cssClip( animate.clip );
		animate.clip = start;
	}

	element.animate( animate, {
		queue: false,
		duration: options.duration,
		easing: options.easing,
		complete: done
	} );

} );


/*!
 * jQuery UI Effects Drop 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Drop Effect
//>>group: Effects
//>>description: Moves an element in one direction and hides it at the same time.
//>>docs: http://api.jqueryui.com/drop-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectDrop = $.effects.define( "drop", "hide", function( options, done ) {

	var distance,
		element = $( this ),
		mode = options.mode,
		show = mode === "show",
		direction = options.direction || "left",
		ref = ( direction === "up" || direction === "down" ) ? "top" : "left",
		motion = ( direction === "up" || direction === "left" ) ? "-=" : "+=",
		oppositeMotion = ( motion === "+=" ) ? "-=" : "+=",
		animation = {
			opacity: 0
		};

	$.effects.createPlaceholder( element );

	distance = options.distance ||
		element[ ref === "top" ? "outerHeight" : "outerWidth" ]( true ) / 2;

	animation[ ref ] = motion + distance;

	if ( show ) {
		element.css( animation );

		animation[ ref ] = oppositeMotion + distance;
		animation.opacity = 1;
	}

	// Animate
	element.animate( animation, {
		queue: false,
		duration: options.duration,
		easing: options.easing,
		complete: done
	} );
} );


/*!
 * jQuery UI Effects Explode 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Explode Effect
//>>group: Effects
// jscs:disable maximumLineLength
//>>description: Explodes an element in all directions into n pieces. Implodes an element to its original wholeness.
// jscs:enable maximumLineLength
//>>docs: http://api.jqueryui.com/explode-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectExplode = $.effects.define( "explode", "hide", function( options, done ) {

	var i, j, left, top, mx, my,
		rows = options.pieces ? Math.round( Math.sqrt( options.pieces ) ) : 3,
		cells = rows,
		element = $( this ),
		mode = options.mode,
		show = mode === "show",

		// Show and then visibility:hidden the element before calculating offset
		offset = element.show().css( "visibility", "hidden" ).offset(),

		// Width and height of a piece
		width = Math.ceil( element.outerWidth() / cells ),
		height = Math.ceil( element.outerHeight() / rows ),
		pieces = [];

	// Children animate complete:
	function childComplete() {
		pieces.push( this );
		if ( pieces.length === rows * cells ) {
			animComplete();
		}
	}

	// Clone the element for each row and cell.
	for ( i = 0; i < rows; i++ ) { // ===>
		top = offset.top + i * height;
		my = i - ( rows - 1 ) / 2;

		for ( j = 0; j < cells; j++ ) { // |||
			left = offset.left + j * width;
			mx = j - ( cells - 1 ) / 2;

			// Create a clone of the now hidden main element that will be absolute positioned
			// within a wrapper div off the -left and -top equal to size of our pieces
			element
				.clone()
				.appendTo( "body" )
				.wrap( "<div></div>" )
				.css( {
					position: "absolute",
					visibility: "visible",
					left: -j * width,
					top: -i * height
				} )

				// Select the wrapper - make it overflow: hidden and absolute positioned based on
				// where the original was located +left and +top equal to the size of pieces
				.parent()
					.addClass( "ui-effects-explode" )
					.css( {
						position: "absolute",
						overflow: "hidden",
						width: width,
						height: height,
						left: left + ( show ? mx * width : 0 ),
						top: top + ( show ? my * height : 0 ),
						opacity: show ? 0 : 1
					} )
					.animate( {
						left: left + ( show ? 0 : mx * width ),
						top: top + ( show ? 0 : my * height ),
						opacity: show ? 1 : 0
					}, options.duration || 500, options.easing, childComplete );
		}
	}

	function animComplete() {
		element.css( {
			visibility: "visible"
		} );
		$( pieces ).remove();
		done();
	}
} );


/*!
 * jQuery UI Effects Fade 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Fade Effect
//>>group: Effects
//>>description: Fades the element.
//>>docs: http://api.jqueryui.com/fade-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectFade = $.effects.define( "fade", "toggle", function( options, done ) {
	var show = options.mode === "show";

	$( this )
		.css( "opacity", show ? 0 : 1 )
		.animate( {
			opacity: show ? 1 : 0
		}, {
			queue: false,
			duration: options.duration,
			easing: options.easing,
			complete: done
		} );
} );


/*!
 * jQuery UI Effects Fold 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Fold Effect
//>>group: Effects
//>>description: Folds an element first horizontally and then vertically.
//>>docs: http://api.jqueryui.com/fold-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectFold = $.effects.define( "fold", "hide", function( options, done ) {

	// Create element
	var element = $( this ),
		mode = options.mode,
		show = mode === "show",
		hide = mode === "hide",
		size = options.size || 15,
		percent = /([0-9]+)%/.exec( size ),
		horizFirst = !!options.horizFirst,
		ref = horizFirst ? [ "right", "bottom" ] : [ "bottom", "right" ],
		duration = options.duration / 2,

		placeholder = $.effects.createPlaceholder( element ),

		start = element.cssClip(),
		animation1 = { clip: $.extend( {}, start ) },
		animation2 = { clip: $.extend( {}, start ) },

		distance = [ start[ ref[ 0 ] ], start[ ref[ 1 ] ] ],

		queuelen = element.queue().length;

	if ( percent ) {
		size = parseInt( percent[ 1 ], 10 ) / 100 * distance[ hide ? 0 : 1 ];
	}
	animation1.clip[ ref[ 0 ] ] = size;
	animation2.clip[ ref[ 0 ] ] = size;
	animation2.clip[ ref[ 1 ] ] = 0;

	if ( show ) {
		element.cssClip( animation2.clip );
		if ( placeholder ) {
			placeholder.css( $.effects.clipToBox( animation2 ) );
		}

		animation2.clip = start;
	}

	// Animate
	element
		.queue( function( next ) {
			if ( placeholder ) {
				placeholder
					.animate( $.effects.clipToBox( animation1 ), duration, options.easing )
					.animate( $.effects.clipToBox( animation2 ), duration, options.easing );
			}

			next();
		} )
		.animate( animation1, duration, options.easing )
		.animate( animation2, duration, options.easing )
		.queue( done );

	$.effects.unshift( element, queuelen, 4 );
} );


/*!
 * jQuery UI Effects Highlight 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Highlight Effect
//>>group: Effects
//>>description: Highlights the background of an element in a defined color for a custom duration.
//>>docs: http://api.jqueryui.com/highlight-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectHighlight = $.effects.define( "highlight", "show", function( options, done ) {
	var element = $( this ),
		animation = {
			backgroundColor: element.css( "backgroundColor" )
		};

	if ( options.mode === "hide" ) {
		animation.opacity = 0;
	}

	$.effects.saveStyle( element );

	element
		.css( {
			backgroundImage: "none",
			backgroundColor: options.color || "#ffff99"
		} )
		.animate( animation, {
			queue: false,
			duration: options.duration,
			easing: options.easing,
			complete: done
		} );
} );


/*!
 * jQuery UI Effects Size 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Size Effect
//>>group: Effects
//>>description: Resize an element to a specified width and height.
//>>docs: http://api.jqueryui.com/size-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectSize = $.effects.define( "size", function( options, done ) {

	// Create element
	var baseline, factor, temp,
		element = $( this ),

		// Copy for children
		cProps = [ "fontSize" ],
		vProps = [ "borderTopWidth", "borderBottomWidth", "paddingTop", "paddingBottom" ],
		hProps = [ "borderLeftWidth", "borderRightWidth", "paddingLeft", "paddingRight" ],

		// Set options
		mode = options.mode,
		restore = mode !== "effect",
		scale = options.scale || "both",
		origin = options.origin || [ "middle", "center" ],
		position = element.css( "position" ),
		pos = element.position(),
		original = $.effects.scaledDimensions( element ),
		from = options.from || original,
		to = options.to || $.effects.scaledDimensions( element, 0 );

	$.effects.createPlaceholder( element );

	if ( mode === "show" ) {
		temp = from;
		from = to;
		to = temp;
	}

	// Set scaling factor
	factor = {
		from: {
			y: from.height / original.height,
			x: from.width / original.width
		},
		to: {
			y: to.height / original.height,
			x: to.width / original.width
		}
	};

	// Scale the css box
	if ( scale === "box" || scale === "both" ) {

		// Vertical props scaling
		if ( factor.from.y !== factor.to.y ) {
			from = $.effects.setTransition( element, vProps, factor.from.y, from );
			to = $.effects.setTransition( element, vProps, factor.to.y, to );
		}

		// Horizontal props scaling
		if ( factor.from.x !== factor.to.x ) {
			from = $.effects.setTransition( element, hProps, factor.from.x, from );
			to = $.effects.setTransition( element, hProps, factor.to.x, to );
		}
	}

	// Scale the content
	if ( scale === "content" || scale === "both" ) {

		// Vertical props scaling
		if ( factor.from.y !== factor.to.y ) {
			from = $.effects.setTransition( element, cProps, factor.from.y, from );
			to = $.effects.setTransition( element, cProps, factor.to.y, to );
		}
	}

	// Adjust the position properties based on the provided origin points
	if ( origin ) {
		baseline = $.effects.getBaseline( origin, original );
		from.top = ( original.outerHeight - from.outerHeight ) * baseline.y + pos.top;
		from.left = ( original.outerWidth - from.outerWidth ) * baseline.x + pos.left;
		to.top = ( original.outerHeight - to.outerHeight ) * baseline.y + pos.top;
		to.left = ( original.outerWidth - to.outerWidth ) * baseline.x + pos.left;
	}
	element.css( from );

	// Animate the children if desired
	if ( scale === "content" || scale === "both" ) {

		vProps = vProps.concat( [ "marginTop", "marginBottom" ] ).concat( cProps );
		hProps = hProps.concat( [ "marginLeft", "marginRight" ] );

		// Only animate children with width attributes specified
		// TODO: is this right? should we include anything with css width specified as well
		element.find( "*[width]" ).each( function() {
			var child = $( this ),
				childOriginal = $.effects.scaledDimensions( child ),
				childFrom = {
					height: childOriginal.height * factor.from.y,
					width: childOriginal.width * factor.from.x,
					outerHeight: childOriginal.outerHeight * factor.from.y,
					outerWidth: childOriginal.outerWidth * factor.from.x
				},
				childTo = {
					height: childOriginal.height * factor.to.y,
					width: childOriginal.width * factor.to.x,
					outerHeight: childOriginal.height * factor.to.y,
					outerWidth: childOriginal.width * factor.to.x
				};

			// Vertical props scaling
			if ( factor.from.y !== factor.to.y ) {
				childFrom = $.effects.setTransition( child, vProps, factor.from.y, childFrom );
				childTo = $.effects.setTransition( child, vProps, factor.to.y, childTo );
			}

			// Horizontal props scaling
			if ( factor.from.x !== factor.to.x ) {
				childFrom = $.effects.setTransition( child, hProps, factor.from.x, childFrom );
				childTo = $.effects.setTransition( child, hProps, factor.to.x, childTo );
			}

			if ( restore ) {
				$.effects.saveStyle( child );
			}

			// Animate children
			child.css( childFrom );
			child.animate( childTo, options.duration, options.easing, function() {

				// Restore children
				if ( restore ) {
					$.effects.restoreStyle( child );
				}
			} );
		} );
	}

	// Animate
	element.animate( to, {
		queue: false,
		duration: options.duration,
		easing: options.easing,
		complete: function() {

			var offset = element.offset();

			if ( to.opacity === 0 ) {
				element.css( "opacity", from.opacity );
			}

			if ( !restore ) {
				element
					.css( "position", position === "static" ? "relative" : position )
					.offset( offset );

				// Need to save style here so that automatic style restoration
				// doesn't restore to the original styles from before the animation.
				$.effects.saveStyle( element );
			}

			done();
		}
	} );

} );


/*!
 * jQuery UI Effects Scale 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Scale Effect
//>>group: Effects
//>>description: Grows or shrinks an element and its content.
//>>docs: http://api.jqueryui.com/scale-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectScale = $.effects.define( "scale", function( options, done ) {

	// Create element
	var el = $( this ),
		mode = options.mode,
		percent = parseInt( options.percent, 10 ) ||
			( parseInt( options.percent, 10 ) === 0 ? 0 : ( mode !== "effect" ? 0 : 100 ) ),

		newOptions = $.extend( true, {
			from: $.effects.scaledDimensions( el ),
			to: $.effects.scaledDimensions( el, percent, options.direction || "both" ),
			origin: options.origin || [ "middle", "center" ]
		}, options );

	// Fade option to support puff
	if ( options.fade ) {
		newOptions.from.opacity = 1;
		newOptions.to.opacity = 0;
	}

	$.effects.effect.size.call( this, newOptions, done );
} );


/*!
 * jQuery UI Effects Puff 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Puff Effect
//>>group: Effects
//>>description: Creates a puff effect by scaling the element up and hiding it at the same time.
//>>docs: http://api.jqueryui.com/puff-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectPuff = $.effects.define( "puff", "hide", function( options, done ) {
	var newOptions = $.extend( true, {}, options, {
		fade: true,
		percent: parseInt( options.percent, 10 ) || 150
	} );

	$.effects.effect.scale.call( this, newOptions, done );
} );


/*!
 * jQuery UI Effects Pulsate 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Pulsate Effect
//>>group: Effects
//>>description: Pulsates an element n times by changing the opacity to zero and back.
//>>docs: http://api.jqueryui.com/pulsate-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectPulsate = $.effects.define( "pulsate", "show", function( options, done ) {
	var element = $( this ),
		mode = options.mode,
		show = mode === "show",
		hide = mode === "hide",
		showhide = show || hide,

		// Showing or hiding leaves off the "last" animation
		anims = ( ( options.times || 5 ) * 2 ) + ( showhide ? 1 : 0 ),
		duration = options.duration / anims,
		animateTo = 0,
		i = 1,
		queuelen = element.queue().length;

	if ( show || !element.is( ":visible" ) ) {
		element.css( "opacity", 0 ).show();
		animateTo = 1;
	}

	// Anims - 1 opacity "toggles"
	for ( ; i < anims; i++ ) {
		element.animate( { opacity: animateTo }, duration, options.easing );
		animateTo = 1 - animateTo;
	}

	element.animate( { opacity: animateTo }, duration, options.easing );

	element.queue( done );

	$.effects.unshift( element, queuelen, anims + 1 );
} );


/*!
 * jQuery UI Effects Shake 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Shake Effect
//>>group: Effects
//>>description: Shakes an element horizontally or vertically n times.
//>>docs: http://api.jqueryui.com/shake-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectShake = $.effects.define( "shake", function( options, done ) {

	var i = 1,
		element = $( this ),
		direction = options.direction || "left",
		distance = options.distance || 20,
		times = options.times || 3,
		anims = times * 2 + 1,
		speed = Math.round( options.duration / anims ),
		ref = ( direction === "up" || direction === "down" ) ? "top" : "left",
		positiveMotion = ( direction === "up" || direction === "left" ),
		animation = {},
		animation1 = {},
		animation2 = {},

		queuelen = element.queue().length;

	$.effects.createPlaceholder( element );

	// Animation
	animation[ ref ] = ( positiveMotion ? "-=" : "+=" ) + distance;
	animation1[ ref ] = ( positiveMotion ? "+=" : "-=" ) + distance * 2;
	animation2[ ref ] = ( positiveMotion ? "-=" : "+=" ) + distance * 2;

	// Animate
	element.animate( animation, speed, options.easing );

	// Shakes
	for ( ; i < times; i++ ) {
		element
			.animate( animation1, speed, options.easing )
			.animate( animation2, speed, options.easing );
	}

	element
		.animate( animation1, speed, options.easing )
		.animate( animation, speed / 2, options.easing )
		.queue( done );

	$.effects.unshift( element, queuelen, anims + 1 );
} );


/*!
 * jQuery UI Effects Slide 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Slide Effect
//>>group: Effects
//>>description: Slides an element in and out of the viewport.
//>>docs: http://api.jqueryui.com/slide-effect/
//>>demos: http://jqueryui.com/effect/



var effectsEffectSlide = $.effects.define( "slide", "show", function( options, done ) {
	var startClip, startRef,
		element = $( this ),
		map = {
			up: [ "bottom", "top" ],
			down: [ "top", "bottom" ],
			left: [ "right", "left" ],
			right: [ "left", "right" ]
		},
		mode = options.mode,
		direction = options.direction || "left",
		ref = ( direction === "up" || direction === "down" ) ? "top" : "left",
		positiveMotion = ( direction === "up" || direction === "left" ),
		distance = options.distance ||
			element[ ref === "top" ? "outerHeight" : "outerWidth" ]( true ),
		animation = {};

	$.effects.createPlaceholder( element );

	startClip = element.cssClip();
	startRef = element.position()[ ref ];

	// Define hide animation
	animation[ ref ] = ( positiveMotion ? -1 : 1 ) * distance + startRef;
	animation.clip = element.cssClip();
	animation.clip[ map[ direction ][ 1 ] ] = animation.clip[ map[ direction ][ 0 ] ];

	// Reverse the animation if we're showing
	if ( mode === "show" ) {
		element.cssClip( animation.clip );
		element.css( ref, animation[ ref ] );
		animation.clip = startClip;
		animation[ ref ] = startRef;
	}

	// Actually animate
	element.animate( animation, {
		queue: false,
		duration: options.duration,
		easing: options.easing,
		complete: done
	} );
} );


/*!
 * jQuery UI Effects Transfer 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Transfer Effect
//>>group: Effects
//>>description: Displays a transfer effect from one element to another.
//>>docs: http://api.jqueryui.com/transfer-effect/
//>>demos: http://jqueryui.com/effect/



var effect;
if ( $.uiBackCompat !== false ) {
	effect = $.effects.define( "transfer", function( options, done ) {
		$( this ).transfer( options, done );
	} );
}
var effectsEffectTransfer = effect;


/*!
 * jQuery UI Focusable 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: :focusable Selector
//>>group: Core
//>>description: Selects elements which can be focused.
//>>docs: http://api.jqueryui.com/focusable-selector/



// Selectors
$.ui.focusable = function( element, hasTabindex ) {
	var map, mapName, img, focusableIfVisible, fieldset,
		nodeName = element.nodeName.toLowerCase();

	if ( "area" === nodeName ) {
		map = element.parentNode;
		mapName = map.name;
		if ( !element.href || !mapName || map.nodeName.toLowerCase() !== "map" ) {
			return false;
		}
		img = $( "img[usemap='#" + mapName + "']" );
		return img.length > 0 && img.is( ":visible" );
	}

	if ( /^(input|select|textarea|button|object)$/.test( nodeName ) ) {
		focusableIfVisible = !element.disabled;

		if ( focusableIfVisible ) {

			// Form controls within a disabled fieldset are disabled.
			// However, controls within the fieldset's legend do not get disabled.
			// Since controls generally aren't placed inside legends, we skip
			// this portion of the check.
			fieldset = $( element ).closest( "fieldset" )[ 0 ];
			if ( fieldset ) {
				focusableIfVisible = !fieldset.disabled;
			}
		}
	} else if ( "a" === nodeName ) {
		focusableIfVisible = element.href || hasTabindex;
	} else {
		focusableIfVisible = hasTabindex;
	}

	return focusableIfVisible && $( element ).is( ":visible" ) && visible( $( element ) );
};

// Support: IE 8 only
// IE 8 doesn't resolve inherit to visible/hidden for computed values
function visible( element ) {
	var visibility = element.css( "visibility" );
	while ( visibility === "inherit" ) {
		element = element.parent();
		visibility = element.css( "visibility" );
	}
	return visibility !== "hidden";
}

$.extend( $.expr[ ":" ], {
	focusable: function( element ) {
		return $.ui.focusable( element, $.attr( element, "tabindex" ) != null );
	}
} );

var focusable = $.ui.focusable;




// Support: IE8 Only
// IE8 does not support the form attribute and when it is supplied. It overwrites the form prop
// with a string, so we need to find the proper form.
var form = $.fn.form = function() {
	return typeof this[ 0 ].form === "string" ? this.closest( "form" ) : $( this[ 0 ].form );
};


/*!
 * jQuery UI Form Reset Mixin 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Form Reset Mixin
//>>group: Core
//>>description: Refresh input widgets when their form is reset
//>>docs: http://api.jqueryui.com/form-reset-mixin/



var formResetMixin = $.ui.formResetMixin = {
	_formResetHandler: function() {
		var form = $( this );

		// Wait for the form reset to actually happen before refreshing
		setTimeout( function() {
			var instances = form.data( "ui-form-reset-instances" );
			$.each( instances, function() {
				this.refresh();
			} );
		} );
	},

	_bindFormResetHandler: function() {
		this.form = this.element.form();
		if ( !this.form.length ) {
			return;
		}

		var instances = this.form.data( "ui-form-reset-instances" ) || [];
		if ( !instances.length ) {

			// We don't use _on() here because we use a single event handler per form
			this.form.on( "reset.ui-form-reset", this._formResetHandler );
		}
		instances.push( this );
		this.form.data( "ui-form-reset-instances", instances );
	},

	_unbindFormResetHandler: function() {
		if ( !this.form.length ) {
			return;
		}

		var instances = this.form.data( "ui-form-reset-instances" );
		instances.splice( $.inArray( this, instances ), 1 );
		if ( instances.length ) {
			this.form.data( "ui-form-reset-instances", instances );
		} else {
			this.form
				.removeData( "ui-form-reset-instances" )
				.off( "reset.ui-form-reset" );
		}
	}
};


/*!
 * jQuery UI Support for jQuery core 1.7.x 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 *
 */

//>>label: jQuery 1.7 Support
//>>group: Core
//>>description: Support version 1.7.x of jQuery core



// Support: jQuery 1.7 only
// Not a great way to check versions, but since we only support 1.7+ and only
// need to detect <1.8, this is a simple check that should suffice. Checking
// for "1.7." would be a bit safer, but the version string is 1.7, not 1.7.0
// and we'll never reach 1.70.0 (if we do, we certainly won't be supporting
// 1.7 anymore). See #11197 for why we're not using feature detection.
if ( $.fn.jquery.substring( 0, 3 ) === "1.7" ) {

	// Setters for .innerWidth(), .innerHeight(), .outerWidth(), .outerHeight()
	// Unlike jQuery Core 1.8+, these only support numeric values to set the
	// dimensions in pixels
	$.each( [ "Width", "Height" ], function( i, name ) {
		var side = name === "Width" ? [ "Left", "Right" ] : [ "Top", "Bottom" ],
			type = name.toLowerCase(),
			orig = {
				innerWidth: $.fn.innerWidth,
				innerHeight: $.fn.innerHeight,
				outerWidth: $.fn.outerWidth,
				outerHeight: $.fn.outerHeight
			};

		function reduce( elem, size, border, margin ) {
			$.each( side, function() {
				size -= parseFloat( $.css( elem, "padding" + this ) ) || 0;
				if ( border ) {
					size -= parseFloat( $.css( elem, "border" + this + "Width" ) ) || 0;
				}
				if ( margin ) {
					size -= parseFloat( $.css( elem, "margin" + this ) ) || 0;
				}
			} );
			return size;
		}

		$.fn[ "inner" + name ] = function( size ) {
			if ( size === undefined ) {
				return orig[ "inner" + name ].call( this );
			}

			return this.each( function() {
				$( this ).css( type, reduce( this, size ) + "px" );
			} );
		};

		$.fn[ "outer" + name ] = function( size, margin ) {
			if ( typeof size !== "number" ) {
				return orig[ "outer" + name ].call( this, size );
			}

			return this.each( function() {
				$( this ).css( type, reduce( this, size, true, margin ) + "px" );
			} );
		};
	} );

	$.fn.addBack = function( selector ) {
		return this.add( selector == null ?
			this.prevObject : this.prevObject.filter( selector )
		);
	};
}

;
/*!
 * jQuery UI Keycode 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Keycode
//>>group: Core
//>>description: Provide keycodes as keynames
//>>docs: http://api.jqueryui.com/jQuery.ui.keyCode/


var keycode = $.ui.keyCode = {
	BACKSPACE: 8,
	COMMA: 188,
	DELETE: 46,
	DOWN: 40,
	END: 35,
	ENTER: 13,
	ESCAPE: 27,
	HOME: 36,
	LEFT: 37,
	PAGE_DOWN: 34,
	PAGE_UP: 33,
	PERIOD: 190,
	RIGHT: 39,
	SPACE: 32,
	TAB: 9,
	UP: 38
};




// Internal use only
var escapeSelector = $.ui.escapeSelector = ( function() {
	var selectorEscape = /([!"#$%&'()*+,./:;<=>?@[\]^`{|}~])/g;
	return function( selector ) {
		return selector.replace( selectorEscape, "\\$1" );
	};
} )();


/*!
 * jQuery UI Labels 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: labels
//>>group: Core
//>>description: Find all the labels associated with a given input
//>>docs: http://api.jqueryui.com/labels/



var labels = $.fn.labels = function() {
	var ancestor, selector, id, labels, ancestors;

	// Check control.labels first
	if ( this[ 0 ].labels && this[ 0 ].labels.length ) {
		return this.pushStack( this[ 0 ].labels );
	}

	// Support: IE <= 11, FF <= 37, Android <= 2.3 only
	// Above browsers do not support control.labels. Everything below is to support them
	// as well as document fragments. control.labels does not work on document fragments
	labels = this.eq( 0 ).parents( "label" );

	// Look for the label based on the id
	id = this.attr( "id" );
	if ( id ) {

		// We don't search against the document in case the element
		// is disconnected from the DOM
		ancestor = this.eq( 0 ).parents().last();

		// Get a full set of top level ancestors
		ancestors = ancestor.add( ancestor.length ? ancestor.siblings() : this.siblings() );

		// Create a selector for the label based on the id
		selector = "label[for='" + $.ui.escapeSelector( id ) + "']";

		labels = labels.add( ancestors.find( selector ).addBack( selector ) );

	}

	// Return whatever we have found for labels
	return this.pushStack( labels );
};


/*!
 * jQuery UI Scroll Parent 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: scrollParent
//>>group: Core
//>>description: Get the closest ancestor element that is scrollable.
//>>docs: http://api.jqueryui.com/scrollParent/



var scrollParent = $.fn.scrollParent = function( includeHidden ) {
	var position = this.css( "position" ),
		excludeStaticParent = position === "absolute",
		overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/,
		scrollParent = this.parents().filter( function() {
			var parent = $( this );
			if ( excludeStaticParent && parent.css( "position" ) === "static" ) {
				return false;
			}
			return overflowRegex.test( parent.css( "overflow" ) + parent.css( "overflow-y" ) +
				parent.css( "overflow-x" ) );
		} ).eq( 0 );

	return position === "fixed" || !scrollParent.length ?
		$( this[ 0 ].ownerDocument || document ) :
		scrollParent;
};


/*!
 * jQuery UI Tabbable 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: :tabbable Selector
//>>group: Core
//>>description: Selects elements which can be tabbed to.
//>>docs: http://api.jqueryui.com/tabbable-selector/



var tabbable = $.extend( $.expr[ ":" ], {
	tabbable: function( element ) {
		var tabIndex = $.attr( element, "tabindex" ),
			hasTabindex = tabIndex != null;
		return ( !hasTabindex || tabIndex >= 0 ) && $.ui.focusable( element, hasTabindex );
	}
} );


/*!
 * jQuery UI Unique ID 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: uniqueId
//>>group: Core
//>>description: Functions to generate and remove uniqueId's
//>>docs: http://api.jqueryui.com/uniqueId/



var uniqueId = $.fn.extend( {
	uniqueId: ( function() {
		var uuid = 0;

		return function() {
			return this.each( function() {
				if ( !this.id ) {
					this.id = "ui-id-" + ( ++uuid );
				}
			} );
		};
	} )(),

	removeUniqueId: function() {
		return this.each( function() {
			if ( /^ui-id-\d+$/.test( this.id ) ) {
				$( this ).removeAttr( "id" );
			}
		} );
	}
} );


/*!
 * jQuery UI Accordion 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Accordion
//>>group: Widgets
// jscs:disable maximumLineLength
//>>description: Displays collapsible content panels for presenting information in a limited amount of space.
// jscs:enable maximumLineLength
//>>docs: http://api.jqueryui.com/accordion/
//>>demos: http://jqueryui.com/accordion/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/accordion.css
//>>css.theme: ../../themes/base/theme.css



var widgetsAccordion = $.widget( "ui.accordion", {
	version: "1.12.1",
	options: {
		active: 0,
		animate: {},
		classes: {
			"ui-accordion-header": "ui-corner-top",
			"ui-accordion-header-collapsed": "ui-corner-all",
			"ui-accordion-content": "ui-corner-bottom"
		},
		collapsible: false,
		event: "click",
		header: "> li > :first-child, > :not(li):even",
		heightStyle: "auto",
		icons: {
			activeHeader: "ui-icon-triangle-1-s",
			header: "ui-icon-triangle-1-e"
		},

		// Callbacks
		activate: null,
		beforeActivate: null
	},

	hideProps: {
		borderTopWidth: "hide",
		borderBottomWidth: "hide",
		paddingTop: "hide",
		paddingBottom: "hide",
		height: "hide"
	},

	showProps: {
		borderTopWidth: "show",
		borderBottomWidth: "show",
		paddingTop: "show",
		paddingBottom: "show",
		height: "show"
	},

	_create: function() {
		var options = this.options;

		this.prevShow = this.prevHide = $();
		this._addClass( "ui-accordion", "ui-widget ui-helper-reset" );
		this.element.attr( "role", "tablist" );

		// Don't allow collapsible: false and active: false / null
		if ( !options.collapsible && ( options.active === false || options.active == null ) ) {
			options.active = 0;
		}

		this._processPanels();

		// handle negative values
		if ( options.active < 0 ) {
			options.active += this.headers.length;
		}
		this._refresh();
	},

	_getCreateEventData: function() {
		return {
			header: this.active,
			panel: !this.active.length ? $() : this.active.next()
		};
	},

	_createIcons: function() {
		var icon, children,
			icons = this.options.icons;

		if ( icons ) {
			icon = $( "<span>" );
			this._addClass( icon, "ui-accordion-header-icon", "ui-icon " + icons.header );
			icon.prependTo( this.headers );
			children = this.active.children( ".ui-accordion-header-icon" );
			this._removeClass( children, icons.header )
				._addClass( children, null, icons.activeHeader )
				._addClass( this.headers, "ui-accordion-icons" );
		}
	},

	_destroyIcons: function() {
		this._removeClass( this.headers, "ui-accordion-icons" );
		this.headers.children( ".ui-accordion-header-icon" ).remove();
	},

	_destroy: function() {
		var contents;

		// Clean up main element
		this.element.removeAttr( "role" );

		// Clean up headers
		this.headers
			.removeAttr( "role aria-expanded aria-selected aria-controls tabIndex" )
			.removeUniqueId();

		this._destroyIcons();

		// Clean up content panels
		contents = this.headers.next()
			.css( "display", "" )
			.removeAttr( "role aria-hidden aria-labelledby" )
			.removeUniqueId();

		if ( this.options.heightStyle !== "content" ) {
			contents.css( "height", "" );
		}
	},

	_setOption: function( key, value ) {
		if ( key === "active" ) {

			// _activate() will handle invalid values and update this.options
			this._activate( value );
			return;
		}

		if ( key === "event" ) {
			if ( this.options.event ) {
				this._off( this.headers, this.options.event );
			}
			this._setupEvents( value );
		}

		this._super( key, value );

		// Setting collapsible: false while collapsed; open first panel
		if ( key === "collapsible" && !value && this.options.active === false ) {
			this._activate( 0 );
		}

		if ( key === "icons" ) {
			this._destroyIcons();
			if ( value ) {
				this._createIcons();
			}
		}
	},

	_setOptionDisabled: function( value ) {
		this._super( value );

		this.element.attr( "aria-disabled", value );

		// Support: IE8 Only
		// #5332 / #6059 - opacity doesn't cascade to positioned elements in IE
		// so we need to add the disabled class to the headers and panels
		this._toggleClass( null, "ui-state-disabled", !!value );
		this._toggleClass( this.headers.add( this.headers.next() ), null, "ui-state-disabled",
			!!value );
	},

	_keydown: function( event ) {
		if ( event.altKey || event.ctrlKey ) {
			return;
		}

		var keyCode = $.ui.keyCode,
			length = this.headers.length,
			currentIndex = this.headers.index( event.target ),
			toFocus = false;

		switch ( event.keyCode ) {
		case keyCode.RIGHT:
		case keyCode.DOWN:
			toFocus = this.headers[ ( currentIndex + 1 ) % length ];
			break;
		case keyCode.LEFT:
		case keyCode.UP:
			toFocus = this.headers[ ( currentIndex - 1 + length ) % length ];
			break;
		case keyCode.SPACE:
		case keyCode.ENTER:
			this._eventHandler( event );
			break;
		case keyCode.HOME:
			toFocus = this.headers[ 0 ];
			break;
		case keyCode.END:
			toFocus = this.headers[ length - 1 ];
			break;
		}

		if ( toFocus ) {
			$( event.target ).attr( "tabIndex", -1 );
			$( toFocus ).attr( "tabIndex", 0 );
			$( toFocus ).trigger( "focus" );
			event.preventDefault();
		}
	},

	_panelKeyDown: function( event ) {
		if ( event.keyCode === $.ui.keyCode.UP && event.ctrlKey ) {
			$( event.currentTarget ).prev().trigger( "focus" );
		}
	},

	refresh: function() {
		var options = this.options;
		this._processPanels();

		// Was collapsed or no panel
		if ( ( options.active === false && options.collapsible === true ) ||
				!this.headers.length ) {
			options.active = false;
			this.active = $();

		// active false only when collapsible is true
		} else if ( options.active === false ) {
			this._activate( 0 );

		// was active, but active panel is gone
		} else if ( this.active.length && !$.contains( this.element[ 0 ], this.active[ 0 ] ) ) {

			// all remaining panel are disabled
			if ( this.headers.length === this.headers.find( ".ui-state-disabled" ).length ) {
				options.active = false;
				this.active = $();

			// activate previous panel
			} else {
				this._activate( Math.max( 0, options.active - 1 ) );
			}

		// was active, active panel still exists
		} else {

			// make sure active index is correct
			options.active = this.headers.index( this.active );
		}

		this._destroyIcons();

		this._refresh();
	},

	_processPanels: function() {
		var prevHeaders = this.headers,
			prevPanels = this.panels;

		this.headers = this.element.find( this.options.header );
		this._addClass( this.headers, "ui-accordion-header ui-accordion-header-collapsed",
			"ui-state-default" );

		this.panels = this.headers.next().filter( ":not(.ui-accordion-content-active)" ).hide();
		this._addClass( this.panels, "ui-accordion-content", "ui-helper-reset ui-widget-content" );

		// Avoid memory leaks (#10056)
		if ( prevPanels ) {
			this._off( prevHeaders.not( this.headers ) );
			this._off( prevPanels.not( this.panels ) );
		}
	},

	_refresh: function() {
		var maxHeight,
			options = this.options,
			heightStyle = options.heightStyle,
			parent = this.element.parent();

		this.active = this._findActive( options.active );
		this._addClass( this.active, "ui-accordion-header-active", "ui-state-active" )
			._removeClass( this.active, "ui-accordion-header-collapsed" );
		this._addClass( this.active.next(), "ui-accordion-content-active" );
		this.active.next().show();

		this.headers
			.attr( "role", "tab" )
			.each( function() {
				var header = $( this ),
					headerId = header.uniqueId().attr( "id" ),
					panel = header.next(),
					panelId = panel.uniqueId().attr( "id" );
				header.attr( "aria-controls", panelId );
				panel.attr( "aria-labelledby", headerId );
			} )
			.next()
				.attr( "role", "tabpanel" );

		this.headers
			.not( this.active )
				.attr( {
					"aria-selected": "false",
					"aria-expanded": "false",
					tabIndex: -1
				} )
				.next()
					.attr( {
						"aria-hidden": "true"
					} )
					.hide();

		// Make sure at least one header is in the tab order
		if ( !this.active.length ) {
			this.headers.eq( 0 ).attr( "tabIndex", 0 );
		} else {
			this.active.attr( {
				"aria-selected": "true",
				"aria-expanded": "true",
				tabIndex: 0
			} )
				.next()
					.attr( {
						"aria-hidden": "false"
					} );
		}

		this._createIcons();

		this._setupEvents( options.event );

		if ( heightStyle === "fill" ) {
			maxHeight = parent.height();
			this.element.siblings( ":visible" ).each( function() {
				var elem = $( this ),
					position = elem.css( "position" );

				if ( position === "absolute" || position === "fixed" ) {
					return;
				}
				maxHeight -= elem.outerHeight( true );
			} );

			this.headers.each( function() {
				maxHeight -= $( this ).outerHeight( true );
			} );

			this.headers.next()
				.each( function() {
					$( this ).height( Math.max( 0, maxHeight -
						$( this ).innerHeight() + $( this ).height() ) );
				} )
				.css( "overflow", "auto" );
		} else if ( heightStyle === "auto" ) {
			maxHeight = 0;
			this.headers.next()
				.each( function() {
					var isVisible = $( this ).is( ":visible" );
					if ( !isVisible ) {
						$( this ).show();
					}
					maxHeight = Math.max( maxHeight, $( this ).css( "height", "" ).height() );
					if ( !isVisible ) {
						$( this ).hide();
					}
				} )
				.height( maxHeight );
		}
	},

	_activate: function( index ) {
		var active = this._findActive( index )[ 0 ];

		// Trying to activate the already active panel
		if ( active === this.active[ 0 ] ) {
			return;
		}

		// Trying to collapse, simulate a click on the currently active header
		active = active || this.active[ 0 ];

		this._eventHandler( {
			target: active,
			currentTarget: active,
			preventDefault: $.noop
		} );
	},

	_findActive: function( selector ) {
		return typeof selector === "number" ? this.headers.eq( selector ) : $();
	},

	_setupEvents: function( event ) {
		var events = {
			keydown: "_keydown"
		};
		if ( event ) {
			$.each( event.split( " " ), function( index, eventName ) {
				events[ eventName ] = "_eventHandler";
			} );
		}

		this._off( this.headers.add( this.headers.next() ) );
		this._on( this.headers, events );
		this._on( this.headers.next(), { keydown: "_panelKeyDown" } );
		this._hoverable( this.headers );
		this._focusable( this.headers );
	},

	_eventHandler: function( event ) {
		var activeChildren, clickedChildren,
			options = this.options,
			active = this.active,
			clicked = $( event.currentTarget ),
			clickedIsActive = clicked[ 0 ] === active[ 0 ],
			collapsing = clickedIsActive && options.collapsible,
			toShow = collapsing ? $() : clicked.next(),
			toHide = active.next(),
			eventData = {
				oldHeader: active,
				oldPanel: toHide,
				newHeader: collapsing ? $() : clicked,
				newPanel: toShow
			};

		event.preventDefault();

		if (

				// click on active header, but not collapsible
				( clickedIsActive && !options.collapsible ) ||

				// allow canceling activation
				( this._trigger( "beforeActivate", event, eventData ) === false ) ) {
			return;
		}

		options.active = collapsing ? false : this.headers.index( clicked );

		// When the call to ._toggle() comes after the class changes
		// it causes a very odd bug in IE 8 (see #6720)
		this.active = clickedIsActive ? $() : clicked;
		this._toggle( eventData );

		// Switch classes
		// corner classes on the previously active header stay after the animation
		this._removeClass( active, "ui-accordion-header-active", "ui-state-active" );
		if ( options.icons ) {
			activeChildren = active.children( ".ui-accordion-header-icon" );
			this._removeClass( activeChildren, null, options.icons.activeHeader )
				._addClass( activeChildren, null, options.icons.header );
		}

		if ( !clickedIsActive ) {
			this._removeClass( clicked, "ui-accordion-header-collapsed" )
				._addClass( clicked, "ui-accordion-header-active", "ui-state-active" );
			if ( options.icons ) {
				clickedChildren = clicked.children( ".ui-accordion-header-icon" );
				this._removeClass( clickedChildren, null, options.icons.header )
					._addClass( clickedChildren, null, options.icons.activeHeader );
			}

			this._addClass( clicked.next(), "ui-accordion-content-active" );
		}
	},

	_toggle: function( data ) {
		var toShow = data.newPanel,
			toHide = this.prevShow.length ? this.prevShow : data.oldPanel;

		// Handle activating a panel during the animation for another activation
		this.prevShow.add( this.prevHide ).stop( true, true );
		this.prevShow = toShow;
		this.prevHide = toHide;

		if ( this.options.animate ) {
			this._animate( toShow, toHide, data );
		} else {
			toHide.hide();
			toShow.show();
			this._toggleComplete( data );
		}

		toHide.attr( {
			"aria-hidden": "true"
		} );
		toHide.prev().attr( {
			"aria-selected": "false",
			"aria-expanded": "false"
		} );

		// if we're switching panels, remove the old header from the tab order
		// if we're opening from collapsed state, remove the previous header from the tab order
		// if we're collapsing, then keep the collapsing header in the tab order
		if ( toShow.length && toHide.length ) {
			toHide.prev().attr( {
				"tabIndex": -1,
				"aria-expanded": "false"
			} );
		} else if ( toShow.length ) {
			this.headers.filter( function() {
				return parseInt( $( this ).attr( "tabIndex" ), 10 ) === 0;
			} )
				.attr( "tabIndex", -1 );
		}

		toShow
			.attr( "aria-hidden", "false" )
			.prev()
				.attr( {
					"aria-selected": "true",
					"aria-expanded": "true",
					tabIndex: 0
				} );
	},

	_animate: function( toShow, toHide, data ) {
		var total, easing, duration,
			that = this,
			adjust = 0,
			boxSizing = toShow.css( "box-sizing" ),
			down = toShow.length &&
				( !toHide.length || ( toShow.index() < toHide.index() ) ),
			animate = this.options.animate || {},
			options = down && animate.down || animate,
			complete = function() {
				that._toggleComplete( data );
			};

		if ( typeof options === "number" ) {
			duration = options;
		}
		if ( typeof options === "string" ) {
			easing = options;
		}

		// fall back from options to animation in case of partial down settings
		easing = easing || options.easing || animate.easing;
		duration = duration || options.duration || animate.duration;

		if ( !toHide.length ) {
			return toShow.animate( this.showProps, duration, easing, complete );
		}
		if ( !toShow.length ) {
			return toHide.animate( this.hideProps, duration, easing, complete );
		}

		total = toShow.show().outerHeight();
		toHide.animate( this.hideProps, {
			duration: duration,
			easing: easing,
			step: function( now, fx ) {
				fx.now = Math.round( now );
			}
		} );
		toShow
			.hide()
			.animate( this.showProps, {
				duration: duration,
				easing: easing,
				complete: complete,
				step: function( now, fx ) {
					fx.now = Math.round( now );
					if ( fx.prop !== "height" ) {
						if ( boxSizing === "content-box" ) {
							adjust += fx.now;
						}
					} else if ( that.options.heightStyle !== "content" ) {
						fx.now = Math.round( total - toHide.outerHeight() - adjust );
						adjust = 0;
					}
				}
			} );
	},

	_toggleComplete: function( data ) {
		var toHide = data.oldPanel,
			prev = toHide.prev();

		this._removeClass( toHide, "ui-accordion-content-active" );
		this._removeClass( prev, "ui-accordion-header-active" )
			._addClass( prev, "ui-accordion-header-collapsed" );

		// Work around for rendering bug in IE (#5421)
		if ( toHide.length ) {
			toHide.parent()[ 0 ].className = toHide.parent()[ 0 ].className;
		}
		this._trigger( "activate", null, data );
	}
} );



var safeActiveElement = $.ui.safeActiveElement = function( document ) {
	var activeElement;

	// Support: IE 9 only
	// IE9 throws an "Unspecified error" accessing document.activeElement from an <iframe>
	try {
		activeElement = document.activeElement;
	} catch ( error ) {
		activeElement = document.body;
	}

	// Support: IE 9 - 11 only
	// IE may return null instead of an element
	// Interestingly, this only seems to occur when NOT in an iframe
	if ( !activeElement ) {
		activeElement = document.body;
	}

	// Support: IE 11 only
	// IE11 returns a seemingly empty object in some cases when accessing
	// document.activeElement from an <iframe>
	if ( !activeElement.nodeName ) {
		activeElement = document.body;
	}

	return activeElement;
};


/*!
 * jQuery UI Menu 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Menu
//>>group: Widgets
//>>description: Creates nestable menus.
//>>docs: http://api.jqueryui.com/menu/
//>>demos: http://jqueryui.com/menu/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/menu.css
//>>css.theme: ../../themes/base/theme.css



var widgetsMenu = $.widget( "ui.menu", {
	version: "1.12.1",
	defaultElement: "<ul>",
	delay: 300,
	options: {
		icons: {
			submenu: "ui-icon-caret-1-e"
		},
		items: "> *",
		menus: "ul",
		position: {
			my: "left top",
			at: "right top"
		},
		role: "menu",

		// Callbacks
		blur: null,
		focus: null,
		select: null
	},

	_create: function() {
		this.activeMenu = this.element;

		// Flag used to prevent firing of the click handler
		// as the event bubbles up through nested menus
		this.mouseHandled = false;
		this.element
			.uniqueId()
			.attr( {
				role: this.options.role,
				tabIndex: 0
			} );

		this._addClass( "ui-menu", "ui-widget ui-widget-content" );
		this._on( {

			// Prevent focus from sticking to links inside menu after clicking
			// them (focus should always stay on UL during navigation).
			"mousedown .ui-menu-item": function( event ) {
				event.preventDefault();
			},
			"click .ui-menu-item": function( event ) {
				var target = $( event.target );
				var active = $( $.ui.safeActiveElement( this.document[ 0 ] ) );
				if ( !this.mouseHandled && target.not( ".ui-state-disabled" ).length ) {
					this.select( event );

					// Only set the mouseHandled flag if the event will bubble, see #9469.
					if ( !event.isPropagationStopped() ) {
						this.mouseHandled = true;
					}

					// Open submenu on click
					if ( target.has( ".ui-menu" ).length ) {
						this.expand( event );
					} else if ( !this.element.is( ":focus" ) &&
							active.closest( ".ui-menu" ).length ) {

						// Redirect focus to the menu
						this.element.trigger( "focus", [ true ] );

						// If the active item is on the top level, let it stay active.
						// Otherwise, blur the active item since it is no longer visible.
						if ( this.active && this.active.parents( ".ui-menu" ).length === 1 ) {
							clearTimeout( this.timer );
						}
					}
				}
			},
			"mouseenter .ui-menu-item": function( event ) {

				// Ignore mouse events while typeahead is active, see #10458.
				// Prevents focusing the wrong item when typeahead causes a scroll while the mouse
				// is over an item in the menu
				if ( this.previousFilter ) {
					return;
				}

				var actualTarget = $( event.target ).closest( ".ui-menu-item" ),
					target = $( event.currentTarget );

				// Ignore bubbled events on parent items, see #11641
				if ( actualTarget[ 0 ] !== target[ 0 ] ) {
					return;
				}

				// Remove ui-state-active class from siblings of the newly focused menu item
				// to avoid a jump caused by adjacent elements both having a class with a border
				this._removeClass( target.siblings().children( ".ui-state-active" ),
					null, "ui-state-active" );
				this.focus( event, target );
			},
			mouseleave: "collapseAll",
			"mouseleave .ui-menu": "collapseAll",
			focus: function( event, keepActiveItem ) {

				// If there's already an active item, keep it active
				// If not, activate the first item
				var item = this.active || this.element.find( this.options.items ).eq( 0 );

				if ( !keepActiveItem ) {
					this.focus( event, item );
				}
			},
			blur: function( event ) {
				this._delay( function() {
					var notContained = !$.contains(
						this.element[ 0 ],
						$.ui.safeActiveElement( this.document[ 0 ] )
					);
					if ( notContained ) {
						this.collapseAll( event );
					}
				} );
			},
			keydown: "_keydown"
		} );

		this.refresh();

		// Clicks outside of a menu collapse any open menus
		this._on( this.document, {
			click: function( event ) {
				if ( this._closeOnDocumentClick( event ) ) {
					this.collapseAll( event );
				}

				// Reset the mouseHandled flag
				this.mouseHandled = false;
			}
		} );
	},

	_destroy: function() {
		var items = this.element.find( ".ui-menu-item" )
				.removeAttr( "role aria-disabled" ),
			submenus = items.children( ".ui-menu-item-wrapper" )
				.removeUniqueId()
				.removeAttr( "tabIndex role aria-haspopup" );

		// Destroy (sub)menus
		this.element
			.removeAttr( "aria-activedescendant" )
			.find( ".ui-menu" ).addBack()
				.removeAttr( "role aria-labelledby aria-expanded aria-hidden aria-disabled " +
					"tabIndex" )
				.removeUniqueId()
				.show();

		submenus.children().each( function() {
			var elem = $( this );
			if ( elem.data( "ui-menu-submenu-caret" ) ) {
				elem.remove();
			}
		} );
	},

	_keydown: function( event ) {
		var match, prev, character, skip,
			preventDefault = true;

		switch ( event.keyCode ) {
		case $.ui.keyCode.PAGE_UP:
			this.previousPage( event );
			break;
		case $.ui.keyCode.PAGE_DOWN:
			this.nextPage( event );
			break;
		case $.ui.keyCode.HOME:
			this._move( "first", "first", event );
			break;
		case $.ui.keyCode.END:
			this._move( "last", "last", event );
			break;
		case $.ui.keyCode.UP:
			this.previous( event );
			break;
		case $.ui.keyCode.DOWN:
			this.next( event );
			break;
		case $.ui.keyCode.LEFT:
			this.collapse( event );
			break;
		case $.ui.keyCode.RIGHT:
			if ( this.active && !this.active.is( ".ui-state-disabled" ) ) {
				this.expand( event );
			}
			break;
		case $.ui.keyCode.ENTER:
		case $.ui.keyCode.SPACE:
			this._activate( event );
			break;
		case $.ui.keyCode.ESCAPE:
			this.collapse( event );
			break;
		default:
			preventDefault = false;
			prev = this.previousFilter || "";
			skip = false;

			// Support number pad values
			character = event.keyCode >= 96 && event.keyCode <= 105 ?
				( event.keyCode - 96 ).toString() : String.fromCharCode( event.keyCode );

			clearTimeout( this.filterTimer );

			if ( character === prev ) {
				skip = true;
			} else {
				character = prev + character;
			}

			match = this._filterMenuItems( character );
			match = skip && match.index( this.active.next() ) !== -1 ?
				this.active.nextAll( ".ui-menu-item" ) :
				match;

			// If no matches on the current filter, reset to the last character pressed
			// to move down the menu to the first item that starts with that character
			if ( !match.length ) {
				character = String.fromCharCode( event.keyCode );
				match = this._filterMenuItems( character );
			}

			if ( match.length ) {
				this.focus( event, match );
				this.previousFilter = character;
				this.filterTimer = this._delay( function() {
					delete this.previousFilter;
				}, 1000 );
			} else {
				delete this.previousFilter;
			}
		}

		if ( preventDefault ) {
			event.preventDefault();
		}
	},

	_activate: function( event ) {
		if ( this.active && !this.active.is( ".ui-state-disabled" ) ) {
			if ( this.active.children( "[aria-haspopup='true']" ).length ) {
				this.expand( event );
			} else {
				this.select( event );
			}
		}
	},

	refresh: function() {
		var menus, items, newSubmenus, newItems, newWrappers,
			that = this,
			icon = this.options.icons.submenu,
			submenus = this.element.find( this.options.menus );

		this._toggleClass( "ui-menu-icons", null, !!this.element.find( ".ui-icon" ).length );

		// Initialize nested menus
		newSubmenus = submenus.filter( ":not(.ui-menu)" )
			.hide()
			.attr( {
				role: this.options.role,
				"aria-hidden": "true",
				"aria-expanded": "false"
			} )
			.each( function() {
				var menu = $( this ),
					item = menu.prev(),
					submenuCaret = $( "<span>" ).data( "ui-menu-submenu-caret", true );

				that._addClass( submenuCaret, "ui-menu-icon", "ui-icon " + icon );
				item
					.attr( "aria-haspopup", "true" )
					.prepend( submenuCaret );
				menu.attr( "aria-labelledby", item.attr( "id" ) );
			} );

		this._addClass( newSubmenus, "ui-menu", "ui-widget ui-widget-content ui-front" );

		menus = submenus.add( this.element );
		items = menus.find( this.options.items );

		// Initialize menu-items containing spaces and/or dashes only as dividers
		items.not( ".ui-menu-item" ).each( function() {
			var item = $( this );
			if ( that._isDivider( item ) ) {
				that._addClass( item, "ui-menu-divider", "ui-widget-content" );
			}
		} );

		// Don't refresh list items that are already adapted
		newItems = items.not( ".ui-menu-item, .ui-menu-divider" );
		newWrappers = newItems.children()
			.not( ".ui-menu" )
				.uniqueId()
				.attr( {
					tabIndex: -1,
					role: this._itemRole()
				} );
		this._addClass( newItems, "ui-menu-item" )
			._addClass( newWrappers, "ui-menu-item-wrapper" );

		// Add aria-disabled attribute to any disabled menu item
		items.filter( ".ui-state-disabled" ).attr( "aria-disabled", "true" );

		// If the active item has been removed, blur the menu
		if ( this.active && !$.contains( this.element[ 0 ], this.active[ 0 ] ) ) {
			this.blur();
		}
	},

	_itemRole: function() {
		return {
			menu: "menuitem",
			listbox: "option"
		}[ this.options.role ];
	},

	_setOption: function( key, value ) {
		if ( key === "icons" ) {
			var icons = this.element.find( ".ui-menu-icon" );
			this._removeClass( icons, null, this.options.icons.submenu )
				._addClass( icons, null, value.submenu );
		}
		this._super( key, value );
	},

	_setOptionDisabled: function( value ) {
		this._super( value );

		this.element.attr( "aria-disabled", String( value ) );
		this._toggleClass( null, "ui-state-disabled", !!value );
	},

	focus: function( event, item ) {
		var nested, focused, activeParent;
		this.blur( event, event && event.type === "focus" );

		this._scrollIntoView( item );

		this.active = item.first();

		focused = this.active.children( ".ui-menu-item-wrapper" );
		this._addClass( focused, null, "ui-state-active" );

		// Only update aria-activedescendant if there's a role
		// otherwise we assume focus is managed elsewhere
		if ( this.options.role ) {
			this.element.attr( "aria-activedescendant", focused.attr( "id" ) );
		}

		// Highlight active parent menu item, if any
		activeParent = this.active
			.parent()
				.closest( ".ui-menu-item" )
					.children( ".ui-menu-item-wrapper" );
		this._addClass( activeParent, null, "ui-state-active" );

		if ( event && event.type === "keydown" ) {
			this._close();
		} else {
			this.timer = this._delay( function() {
				this._close();
			}, this.delay );
		}

		nested = item.children( ".ui-menu" );
		if ( nested.length && event && ( /^mouse/.test( event.type ) ) ) {
			this._startOpening( nested );
		}
		this.activeMenu = item.parent();

		this._trigger( "focus", event, { item: item } );
	},

	_scrollIntoView: function( item ) {
		var borderTop, paddingTop, offset, scroll, elementHeight, itemHeight;
		if ( this._hasScroll() ) {
			borderTop = parseFloat( $.css( this.activeMenu[ 0 ], "borderTopWidth" ) ) || 0;
			paddingTop = parseFloat( $.css( this.activeMenu[ 0 ], "paddingTop" ) ) || 0;
			offset = item.offset().top - this.activeMenu.offset().top - borderTop - paddingTop;
			scroll = this.activeMenu.scrollTop();
			elementHeight = this.activeMenu.height();
			itemHeight = item.outerHeight();

			if ( offset < 0 ) {
				this.activeMenu.scrollTop( scroll + offset );
			} else if ( offset + itemHeight > elementHeight ) {
				this.activeMenu.scrollTop( scroll + offset - elementHeight + itemHeight );
			}
		}
	},

	blur: function( event, fromFocus ) {
		if ( !fromFocus ) {
			clearTimeout( this.timer );
		}

		if ( !this.active ) {
			return;
		}

		this._removeClass( this.active.children( ".ui-menu-item-wrapper" ),
			null, "ui-state-active" );

		this._trigger( "blur", event, { item: this.active } );
		this.active = null;
	},

	_startOpening: function( submenu ) {
		clearTimeout( this.timer );

		// Don't open if already open fixes a Firefox bug that caused a .5 pixel
		// shift in the submenu position when mousing over the caret icon
		if ( submenu.attr( "aria-hidden" ) !== "true" ) {
			return;
		}

		this.timer = this._delay( function() {
			this._close();
			this._open( submenu );
		}, this.delay );
	},

	_open: function( submenu ) {
		var position = $.extend( {
			of: this.active
		}, this.options.position );

		clearTimeout( this.timer );
		this.element.find( ".ui-menu" ).not( submenu.parents( ".ui-menu" ) )
			.hide()
			.attr( "aria-hidden", "true" );

		submenu
			.show()
			.removeAttr( "aria-hidden" )
			.attr( "aria-expanded", "true" )
			.position( position );
	},

	collapseAll: function( event, all ) {
		clearTimeout( this.timer );
		this.timer = this._delay( function() {

			// If we were passed an event, look for the submenu that contains the event
			var currentMenu = all ? this.element :
				$( event && event.target ).closest( this.element.find( ".ui-menu" ) );

			// If we found no valid submenu ancestor, use the main menu to close all
			// sub menus anyway
			if ( !currentMenu.length ) {
				currentMenu = this.element;
			}

			this._close( currentMenu );

			this.blur( event );

			// Work around active item staying active after menu is blurred
			this._removeClass( currentMenu.find( ".ui-state-active" ), null, "ui-state-active" );

			this.activeMenu = currentMenu;
		}, this.delay );
	},

	// With no arguments, closes the currently active menu - if nothing is active
	// it closes all menus.  If passed an argument, it will search for menus BELOW
	_close: function( startMenu ) {
		if ( !startMenu ) {
			startMenu = this.active ? this.active.parent() : this.element;
		}

		startMenu.find( ".ui-menu" )
			.hide()
			.attr( "aria-hidden", "true" )
			.attr( "aria-expanded", "false" );
	},

	_closeOnDocumentClick: function( event ) {
		return !$( event.target ).closest( ".ui-menu" ).length;
	},

	_isDivider: function( item ) {

		// Match hyphen, em dash, en dash
		return !/[^\-\u2014\u2013\s]/.test( item.text() );
	},

	collapse: function( event ) {
		var newItem = this.active &&
			this.active.parent().closest( ".ui-menu-item", this.element );
		if ( newItem && newItem.length ) {
			this._close();
			this.focus( event, newItem );
		}
	},

	expand: function( event ) {
		var newItem = this.active &&
			this.active
				.children( ".ui-menu " )
					.find( this.options.items )
						.first();

		if ( newItem && newItem.length ) {
			this._open( newItem.parent() );

			// Delay so Firefox will not hide activedescendant change in expanding submenu from AT
			this._delay( function() {
				this.focus( event, newItem );
			} );
		}
	},

	next: function( event ) {
		this._move( "next", "first", event );
	},

	previous: function( event ) {
		this._move( "prev", "last", event );
	},

	isFirstItem: function() {
		return this.active && !this.active.prevAll( ".ui-menu-item" ).length;
	},

	isLastItem: function() {
		return this.active && !this.active.nextAll( ".ui-menu-item" ).length;
	},

	_move: function( direction, filter, event ) {
		var next;
		if ( this.active ) {
			if ( direction === "first" || direction === "last" ) {
				next = this.active
					[ direction === "first" ? "prevAll" : "nextAll" ]( ".ui-menu-item" )
					.eq( -1 );
			} else {
				next = this.active
					[ direction + "All" ]( ".ui-menu-item" )
					.eq( 0 );
			}
		}
		if ( !next || !next.length || !this.active ) {
			next = this.activeMenu.find( this.options.items )[ filter ]();
		}

		this.focus( event, next );
	},

	nextPage: function( event ) {
		var item, base, height;

		if ( !this.active ) {
			this.next( event );
			return;
		}
		if ( this.isLastItem() ) {
			return;
		}
		if ( this._hasScroll() ) {
			base = this.active.offset().top;
			height = this.element.height();
			this.active.nextAll( ".ui-menu-item" ).each( function() {
				item = $( this );
				return item.offset().top - base - height < 0;
			} );

			this.focus( event, item );
		} else {
			this.focus( event, this.activeMenu.find( this.options.items )
				[ !this.active ? "first" : "last" ]() );
		}
	},

	previousPage: function( event ) {
		var item, base, height;
		if ( !this.active ) {
			this.next( event );
			return;
		}
		if ( this.isFirstItem() ) {
			return;
		}
		if ( this._hasScroll() ) {
			base = this.active.offset().top;
			height = this.element.height();
			this.active.prevAll( ".ui-menu-item" ).each( function() {
				item = $( this );
				return item.offset().top - base + height > 0;
			} );

			this.focus( event, item );
		} else {
			this.focus( event, this.activeMenu.find( this.options.items ).first() );
		}
	},

	_hasScroll: function() {
		return this.element.outerHeight() < this.element.prop( "scrollHeight" );
	},

	select: function( event ) {

		// TODO: It should never be possible to not have an active item at this
		// point, but the tests don't trigger mouseenter before click.
		this.active = this.active || $( event.target ).closest( ".ui-menu-item" );
		var ui = { item: this.active };
		if ( !this.active.has( ".ui-menu" ).length ) {
			this.collapseAll( event, true );
		}
		this._trigger( "select", event, ui );
	},

	_filterMenuItems: function( character ) {
		var escapedCharacter = character.replace( /[\-\[\]{}()*+?.,\\\^$|#\s]/g, "\\$&" ),
			regex = new RegExp( "^" + escapedCharacter, "i" );

		return this.activeMenu
			.find( this.options.items )

				// Only match on items, not dividers or other content (#10571)
				.filter( ".ui-menu-item" )
					.filter( function() {
						return regex.test(
							$.trim( $( this ).children( ".ui-menu-item-wrapper" ).text() ) );
					} );
	}
} );


/*!
 * jQuery UI Autocomplete 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Autocomplete
//>>group: Widgets
//>>description: Lists suggested words as the user is typing.
//>>docs: http://api.jqueryui.com/autocomplete/
//>>demos: http://jqueryui.com/autocomplete/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/autocomplete.css
//>>css.theme: ../../themes/base/theme.css



$.widget( "ui.autocomplete", {
	version: "1.12.1",
	defaultElement: "<input>",
	options: {
		appendTo: null,
		autoFocus: false,
		delay: 300,
		minLength: 1,
		position: {
			my: "left top",
			at: "left bottom",
			collision: "none"
		},
		source: null,

		// Callbacks
		change: null,
		close: null,
		focus: null,
		open: null,
		response: null,
		search: null,
		select: null
	},

	requestIndex: 0,
	pending: 0,

	_create: function() {

		// Some browsers only repeat keydown events, not keypress events,
		// so we use the suppressKeyPress flag to determine if we've already
		// handled the keydown event. #7269
		// Unfortunately the code for & in keypress is the same as the up arrow,
		// so we use the suppressKeyPressRepeat flag to avoid handling keypress
		// events when we know the keydown event was used to modify the
		// search term. #7799
		var suppressKeyPress, suppressKeyPressRepeat, suppressInput,
			nodeName = this.element[ 0 ].nodeName.toLowerCase(),
			isTextarea = nodeName === "textarea",
			isInput = nodeName === "input";

		// Textareas are always multi-line
		// Inputs are always single-line, even if inside a contentEditable element
		// IE also treats inputs as contentEditable
		// All other element types are determined by whether or not they're contentEditable
		this.isMultiLine = isTextarea || !isInput && this._isContentEditable( this.element );

		this.valueMethod = this.element[ isTextarea || isInput ? "val" : "text" ];
		this.isNewMenu = true;

		this._addClass( "ui-autocomplete-input" );
		this.element.attr( "autocomplete", "off" );

		this._on( this.element, {
			keydown: function( event ) {
				if ( this.element.prop( "readOnly" ) ) {
					suppressKeyPress = true;
					suppressInput = true;
					suppressKeyPressRepeat = true;
					return;
				}

				suppressKeyPress = false;
				suppressInput = false;
				suppressKeyPressRepeat = false;
				var keyCode = $.ui.keyCode;
				switch ( event.keyCode ) {
				case keyCode.PAGE_UP:
					suppressKeyPress = true;
					this._move( "previousPage", event );
					break;
				case keyCode.PAGE_DOWN:
					suppressKeyPress = true;
					this._move( "nextPage", event );
					break;
				case keyCode.UP:
					suppressKeyPress = true;
					this._keyEvent( "previous", event );
					break;
				case keyCode.DOWN:
					suppressKeyPress = true;
					this._keyEvent( "next", event );
					break;
				case keyCode.ENTER:

					// when menu is open and has focus
					if ( this.menu.active ) {

						// #6055 - Opera still allows the keypress to occur
						// which causes forms to submit
						suppressKeyPress = true;
						event.preventDefault();
						this.menu.select( event );
					}
					break;
				case keyCode.TAB:
					if ( this.menu.active ) {
						this.menu.select( event );
					}
					break;
				case keyCode.ESCAPE:
					if ( this.menu.element.is( ":visible" ) ) {
						if ( !this.isMultiLine ) {
							this._value( this.term );
						}
						this.close( event );

						// Different browsers have different default behavior for escape
						// Single press can mean undo or clear
						// Double press in IE means clear the whole form
						event.preventDefault();
					}
					break;
				default:
					suppressKeyPressRepeat = true;

					// search timeout should be triggered before the input value is changed
					this._searchTimeout( event );
					break;
				}
			},
			keypress: function( event ) {
				if ( suppressKeyPress ) {
					suppressKeyPress = false;
					if ( !this.isMultiLine || this.menu.element.is( ":visible" ) ) {
						event.preventDefault();
					}
					return;
				}
				if ( suppressKeyPressRepeat ) {
					return;
				}

				// Replicate some key handlers to allow them to repeat in Firefox and Opera
				var keyCode = $.ui.keyCode;
				switch ( event.keyCode ) {
				case keyCode.PAGE_UP:
					this._move( "previousPage", event );
					break;
				case keyCode.PAGE_DOWN:
					this._move( "nextPage", event );
					break;
				case keyCode.UP:
					this._keyEvent( "previous", event );
					break;
				case keyCode.DOWN:
					this._keyEvent( "next", event );
					break;
				}
			},
			input: function( event ) {
				if ( suppressInput ) {
					suppressInput = false;
					event.preventDefault();
					return;
				}
				this._searchTimeout( event );
			},
			focus: function() {
				this.selectedItem = null;
				this.previous = this._value();
			},
			blur: function( event ) {
				if ( this.cancelBlur ) {
					delete this.cancelBlur;
					return;
				}

				clearTimeout( this.searching );
				this.close( event );
				this._change( event );
			}
		} );

		this._initSource();
		this.menu = $( "<ul>" )
			.appendTo( this._appendTo() )
			.menu( {

				// disable ARIA support, the live region takes care of that
				role: null
			} )
			.hide()
			.menu( "instance" );

		this._addClass( this.menu.element, "ui-autocomplete", "ui-front" );
		this._on( this.menu.element, {
			mousedown: function( event ) {

				// prevent moving focus out of the text field
				event.preventDefault();

				// IE doesn't prevent moving focus even with event.preventDefault()
				// so we set a flag to know when we should ignore the blur event
				this.cancelBlur = true;
				this._delay( function() {
					delete this.cancelBlur;

					// Support: IE 8 only
					// Right clicking a menu item or selecting text from the menu items will
					// result in focus moving out of the input. However, we've already received
					// and ignored the blur event because of the cancelBlur flag set above. So
					// we restore focus to ensure that the menu closes properly based on the user's
					// next actions.
					if ( this.element[ 0 ] !== $.ui.safeActiveElement( this.document[ 0 ] ) ) {
						this.element.trigger( "focus" );
					}
				} );
			},
			menufocus: function( event, ui ) {
				var label, item;

				// support: Firefox
				// Prevent accidental activation of menu items in Firefox (#7024 #9118)
				if ( this.isNewMenu ) {
					this.isNewMenu = false;
					if ( event.originalEvent && /^mouse/.test( event.originalEvent.type ) ) {
						this.menu.blur();

						this.document.one( "mousemove", function() {
							$( event.target ).trigger( event.originalEvent );
						} );

						return;
					}
				}

				item = ui.item.data( "ui-autocomplete-item" );
				if ( false !== this._trigger( "focus", event, { item: item } ) ) {

					// use value to match what will end up in the input, if it was a key event
					if ( event.originalEvent && /^key/.test( event.originalEvent.type ) ) {
						this._value( item.value );
					}
				}

				// Announce the value in the liveRegion
				label = ui.item.attr( "aria-label" ) || item.value;
				if ( label && $.trim( label ).length ) {
					this.liveRegion.children().hide();
					$( "<div>" ).text( label ).appendTo( this.liveRegion );
				}
			},
			menuselect: function( event, ui ) {
				var item = ui.item.data( "ui-autocomplete-item" ),
					previous = this.previous;

				// Only trigger when focus was lost (click on menu)
				if ( this.element[ 0 ] !== $.ui.safeActiveElement( this.document[ 0 ] ) ) {
					this.element.trigger( "focus" );
					this.previous = previous;

					// #6109 - IE triggers two focus events and the second
					// is asynchronous, so we need to reset the previous
					// term synchronously and asynchronously :-(
					this._delay( function() {
						this.previous = previous;
						this.selectedItem = item;
					} );
				}

				if ( false !== this._trigger( "select", event, { item: item } ) ) {
					this._value( item.value );
				}

				// reset the term after the select event
				// this allows custom select handling to work properly
				this.term = this._value();

				this.close( event );
				this.selectedItem = item;
			}
		} );

		this.liveRegion = $( "<div>", {
			role: "status",
			"aria-live": "assertive",
			"aria-relevant": "additions"
		} )
			.appendTo( this.document[ 0 ].body );

		this._addClass( this.liveRegion, null, "ui-helper-hidden-accessible" );

		// Turning off autocomplete prevents the browser from remembering the
		// value when navigating through history, so we re-enable autocomplete
		// if the page is unloaded before the widget is destroyed. #7790
		this._on( this.window, {
			beforeunload: function() {
				this.element.removeAttr( "autocomplete" );
			}
		} );
	},

	_destroy: function() {
		clearTimeout( this.searching );
		this.element.removeAttr( "autocomplete" );
		this.menu.element.remove();
		this.liveRegion.remove();
	},

	_setOption: function( key, value ) {
		this._super( key, value );
		if ( key === "source" ) {
			this._initSource();
		}
		if ( key === "appendTo" ) {
			this.menu.element.appendTo( this._appendTo() );
		}
		if ( key === "disabled" && value && this.xhr ) {
			this.xhr.abort();
		}
	},

	_isEventTargetInWidget: function( event ) {
		var menuElement = this.menu.element[ 0 ];

		return event.target === this.element[ 0 ] ||
			event.target === menuElement ||
			$.contains( menuElement, event.target );
	},

	_closeOnClickOutside: function( event ) {
		if ( !this._isEventTargetInWidget( event ) ) {
			this.close();
		}
	},

	_appendTo: function() {
		var element = this.options.appendTo;

		if ( element ) {
			element = element.jquery || element.nodeType ?
				$( element ) :
				this.document.find( element ).eq( 0 );
		}

		if ( !element || !element[ 0 ] ) {
			element = this.element.closest( ".ui-front, dialog" );
		}

		if ( !element.length ) {
			element = this.document[ 0 ].body;
		}

		return element;
	},

	_initSource: function() {
		var array, url,
			that = this;
		if ( $.isArray( this.options.source ) ) {
			array = this.options.source;
			this.source = function( request, response ) {
				response( $.ui.autocomplete.filter( array, request.term ) );
			};
		} else if ( typeof this.options.source === "string" ) {
			url = this.options.source;
			this.source = function( request, response ) {
				if ( that.xhr ) {
					that.xhr.abort();
				}
				that.xhr = $.ajax( {
					url: url,
					data: request,
					dataType: "json",
					success: function( data ) {
						response( data );
					},
					error: function() {
						response( [] );
					}
				} );
			};
		} else {
			this.source = this.options.source;
		}
	},

	_searchTimeout: function( event ) {
		clearTimeout( this.searching );
		this.searching = this._delay( function() {

			// Search if the value has changed, or if the user retypes the same value (see #7434)
			var equalValues = this.term === this._value(),
				menuVisible = this.menu.element.is( ":visible" ),
				modifierKey = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;

			if ( !equalValues || ( equalValues && !menuVisible && !modifierKey ) ) {
				this.selectedItem = null;
				this.search( null, event );
			}
		}, this.options.delay );
	},

	search: function( value, event ) {
		value = value != null ? value : this._value();

		// Always save the actual value, not the one passed as an argument
		this.term = this._value();

		if ( value.length < this.options.minLength ) {
			return this.close( event );
		}

		if ( this._trigger( "search", event ) === false ) {
			return;
		}

		return this._search( value );
	},

	_search: function( value ) {
		this.pending++;
		this._addClass( "ui-autocomplete-loading" );
		this.cancelSearch = false;

		this.source( { term: value }, this._response() );
	},

	_response: function() {
		var index = ++this.requestIndex;

		return $.proxy( function( content ) {
			if ( index === this.requestIndex ) {
				this.__response( content );
			}

			this.pending--;
			if ( !this.pending ) {
				this._removeClass( "ui-autocomplete-loading" );
			}
		}, this );
	},

	__response: function( content ) {
		if ( content ) {
			content = this._normalize( content );
		}
		this._trigger( "response", null, { content: content } );
		if ( !this.options.disabled && content && content.length && !this.cancelSearch ) {
			this._suggest( content );
			this._trigger( "open" );
		} else {

			// use ._close() instead of .close() so we don't cancel future searches
			this._close();
		}
	},

	close: function( event ) {
		this.cancelSearch = true;
		this._close( event );
	},

	_close: function( event ) {

		// Remove the handler that closes the menu on outside clicks
		this._off( this.document, "mousedown" );

		if ( this.menu.element.is( ":visible" ) ) {
			this.menu.element.hide();
			this.menu.blur();
			this.isNewMenu = true;
			this._trigger( "close", event );
		}
	},

	_change: function( event ) {
		if ( this.previous !== this._value() ) {
			this._trigger( "change", event, { item: this.selectedItem } );
		}
	},

	_normalize: function( items ) {

		// assume all items have the right format when the first item is complete
		if ( items.length && items[ 0 ].label && items[ 0 ].value ) {
			return items;
		}
		return $.map( items, function( item ) {
			if ( typeof item === "string" ) {
				return {
					label: item,
					value: item
				};
			}
			return $.extend( {}, item, {
				label: item.label || item.value,
				value: item.value || item.label
			} );
		} );
	},

	_suggest: function( items ) {
		var ul = this.menu.element.empty();
		this._renderMenu( ul, items );
		this.isNewMenu = true;
		this.menu.refresh();

		// Size and position menu
		ul.show();
		this._resizeMenu();
		ul.position( $.extend( {
			of: this.element
		}, this.options.position ) );

		if ( this.options.autoFocus ) {
			this.menu.next();
		}

		// Listen for interactions outside of the widget (#6642)
		this._on( this.document, {
			mousedown: "_closeOnClickOutside"
		} );
	},

	_resizeMenu: function() {
		var ul = this.menu.element;
		ul.outerWidth( Math.max(

			// Firefox wraps long text (possibly a rounding bug)
			// so we add 1px to avoid the wrapping (#7513)
			ul.width( "" ).outerWidth() + 1,
			this.element.outerWidth()
		) );
	},

	_renderMenu: function( ul, items ) {
		var that = this;
		$.each( items, function( index, item ) {
			that._renderItemData( ul, item );
		} );
	},

	_renderItemData: function( ul, item ) {
		return this._renderItem( ul, item ).data( "ui-autocomplete-item", item );
	},

	_renderItem: function( ul, item ) {
		return $( "<li>" )
			.append( $( "<div>" ).text( item.label ) )
			.appendTo( ul );
	},

	_move: function( direction, event ) {
		if ( !this.menu.element.is( ":visible" ) ) {
			this.search( null, event );
			return;
		}
		if ( this.menu.isFirstItem() && /^previous/.test( direction ) ||
				this.menu.isLastItem() && /^next/.test( direction ) ) {

			if ( !this.isMultiLine ) {
				this._value( this.term );
			}

			this.menu.blur();
			return;
		}
		this.menu[ direction ]( event );
	},

	widget: function() {
		return this.menu.element;
	},

	_value: function() {
		return this.valueMethod.apply( this.element, arguments );
	},

	_keyEvent: function( keyEvent, event ) {
		if ( !this.isMultiLine || this.menu.element.is( ":visible" ) ) {
			this._move( keyEvent, event );

			// Prevents moving cursor to beginning/end of the text field in some browsers
			event.preventDefault();
		}
	},

	// Support: Chrome <=50
	// We should be able to just use this.element.prop( "isContentEditable" )
	// but hidden elements always report false in Chrome.
	// https://code.google.com/p/chromium/issues/detail?id=313082
	_isContentEditable: function( element ) {
		if ( !element.length ) {
			return false;
		}

		var editable = element.prop( "contentEditable" );

		if ( editable === "inherit" ) {
		  return this._isContentEditable( element.parent() );
		}

		return editable === "true";
	}
} );

$.extend( $.ui.autocomplete, {
	escapeRegex: function( value ) {
		return value.replace( /[\-\[\]{}()*+?.,\\\^$|#\s]/g, "\\$&" );
	},
	filter: function( array, term ) {
		var matcher = new RegExp( $.ui.autocomplete.escapeRegex( term ), "i" );
		return $.grep( array, function( value ) {
			return matcher.test( value.label || value.value || value );
		} );
	}
} );

// Live region extension, adding a `messages` option
// NOTE: This is an experimental API. We are still investigating
// a full solution for string manipulation and internationalization.
$.widget( "ui.autocomplete", $.ui.autocomplete, {
	options: {
		messages: {
			noResults: "No search results.",
			results: function( amount ) {
				return amount + ( amount > 1 ? " results are" : " result is" ) +
					" available, use up and down arrow keys to navigate.";
			}
		}
	},

	__response: function( content ) {
		var message;
		this._superApply( arguments );
		if ( this.options.disabled || this.cancelSearch ) {
			return;
		}
		if ( content && content.length ) {
			message = this.options.messages.results( content.length );
		} else {
			message = this.options.messages.noResults;
		}
		this.liveRegion.children().hide();
		$( "<div>" ).text( message ).appendTo( this.liveRegion );
	}
} );

var widgetsAutocomplete = $.ui.autocomplete;


/*!
 * jQuery UI Controlgroup 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Controlgroup
//>>group: Widgets
//>>description: Visually groups form control widgets
//>>docs: http://api.jqueryui.com/controlgroup/
//>>demos: http://jqueryui.com/controlgroup/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/controlgroup.css
//>>css.theme: ../../themes/base/theme.css


var controlgroupCornerRegex = /ui-corner-([a-z]){2,6}/g;

var widgetsControlgroup = $.widget( "ui.controlgroup", {
	version: "1.12.1",
	defaultElement: "<div>",
	options: {
		direction: "horizontal",
		disabled: null,
		onlyVisible: true,
		items: {
			"button": "input[type=button], input[type=submit], input[type=reset], button, a",
			"controlgroupLabel": ".ui-controlgroup-label",
			"checkboxradio": "input[type='checkbox'], input[type='radio']",
			"selectmenu": "select",
			"spinner": ".ui-spinner-input"
		}
	},

	_create: function() {
		this._enhance();
	},

	// To support the enhanced option in jQuery Mobile, we isolate DOM manipulation
	_enhance: function() {
		this.element.attr( "role", "toolbar" );
		this.refresh();
	},

	_destroy: function() {
		this._callChildMethod( "destroy" );
		this.childWidgets.removeData( "ui-controlgroup-data" );
		this.element.removeAttr( "role" );
		if ( this.options.items.controlgroupLabel ) {
			this.element
				.find( this.options.items.controlgroupLabel )
				.find( ".ui-controlgroup-label-contents" )
				.contents().unwrap();
		}
	},

	_initWidgets: function() {
		var that = this,
			childWidgets = [];

		// First we iterate over each of the items options
		$.each( this.options.items, function( widget, selector ) {
			var labels;
			var options = {};

			// Make sure the widget has a selector set
			if ( !selector ) {
				return;
			}

			if ( widget === "controlgroupLabel" ) {
				labels = that.element.find( selector );
				labels.each( function() {
					var element = $( this );

					if ( element.children( ".ui-controlgroup-label-contents" ).length ) {
						return;
					}
					element.contents()
						.wrapAll( "<span class='ui-controlgroup-label-contents'></span>" );
				} );
				that._addClass( labels, null, "ui-widget ui-widget-content ui-state-default" );
				childWidgets = childWidgets.concat( labels.get() );
				return;
			}

			// Make sure the widget actually exists
			if ( !$.fn[ widget ] ) {
				return;
			}

			// We assume everything is in the middle to start because we can't determine
			// first / last elements until all enhancments are done.
			if ( that[ "_" + widget + "Options" ] ) {
				options = that[ "_" + widget + "Options" ]( "middle" );
			} else {
				options = { classes: {} };
			}

			// Find instances of this widget inside controlgroup and init them
			that.element
				.find( selector )
				.each( function() {
					var element = $( this );
					var instance = element[ widget ]( "instance" );

					// We need to clone the default options for this type of widget to avoid
					// polluting the variable options which has a wider scope than a single widget.
					var instanceOptions = $.widget.extend( {}, options );

					// If the button is the child of a spinner ignore it
					// TODO: Find a more generic solution
					if ( widget === "button" && element.parent( ".ui-spinner" ).length ) {
						return;
					}

					// Create the widget if it doesn't exist
					if ( !instance ) {
						instance = element[ widget ]()[ widget ]( "instance" );
					}
					if ( instance ) {
						instanceOptions.classes =
							that._resolveClassesValues( instanceOptions.classes, instance );
					}
					element[ widget ]( instanceOptions );

					// Store an instance of the controlgroup to be able to reference
					// from the outermost element for changing options and refresh
					var widgetElement = element[ widget ]( "widget" );
					$.data( widgetElement[ 0 ], "ui-controlgroup-data",
						instance ? instance : element[ widget ]( "instance" ) );

					childWidgets.push( widgetElement[ 0 ] );
				} );
		} );

		this.childWidgets = $( $.unique( childWidgets ) );
		this._addClass( this.childWidgets, "ui-controlgroup-item" );
	},

	_callChildMethod: function( method ) {
		this.childWidgets.each( function() {
			var element = $( this ),
				data = element.data( "ui-controlgroup-data" );
			if ( data && data[ method ] ) {
				data[ method ]();
			}
		} );
	},

	_updateCornerClass: function( element, position ) {
		var remove = "ui-corner-top ui-corner-bottom ui-corner-left ui-corner-right ui-corner-all";
		var add = this._buildSimpleOptions( position, "label" ).classes.label;

		this._removeClass( element, null, remove );
		this._addClass( element, null, add );
	},

	_buildSimpleOptions: function( position, key ) {
		var direction = this.options.direction === "vertical";
		var result = {
			classes: {}
		};
		result.classes[ key ] = {
			"middle": "",
			"first": "ui-corner-" + ( direction ? "top" : "left" ),
			"last": "ui-corner-" + ( direction ? "bottom" : "right" ),
			"only": "ui-corner-all"
		}[ position ];

		return result;
	},

	_spinnerOptions: function( position ) {
		var options = this._buildSimpleOptions( position, "ui-spinner" );

		options.classes[ "ui-spinner-up" ] = "";
		options.classes[ "ui-spinner-down" ] = "";

		return options;
	},

	_buttonOptions: function( position ) {
		return this._buildSimpleOptions( position, "ui-button" );
	},

	_checkboxradioOptions: function( position ) {
		return this._buildSimpleOptions( position, "ui-checkboxradio-label" );
	},

	_selectmenuOptions: function( position ) {
		var direction = this.options.direction === "vertical";
		return {
			width: direction ? "auto" : false,
			classes: {
				middle: {
					"ui-selectmenu-button-open": "",
					"ui-selectmenu-button-closed": ""
				},
				first: {
					"ui-selectmenu-button-open": "ui-corner-" + ( direction ? "top" : "tl" ),
					"ui-selectmenu-button-closed": "ui-corner-" + ( direction ? "top" : "left" )
				},
				last: {
					"ui-selectmenu-button-open": direction ? "" : "ui-corner-tr",
					"ui-selectmenu-button-closed": "ui-corner-" + ( direction ? "bottom" : "right" )
				},
				only: {
					"ui-selectmenu-button-open": "ui-corner-top",
					"ui-selectmenu-button-closed": "ui-corner-all"
				}

			}[ position ]
		};
	},

	_resolveClassesValues: function( classes, instance ) {
		var result = {};
		$.each( classes, function( key ) {
			var current = instance.options.classes[ key ] || "";
			current = $.trim( current.replace( controlgroupCornerRegex, "" ) );
			result[ key ] = ( current + " " + classes[ key ] ).replace( /\s+/g, " " );
		} );
		return result;
	},

	_setOption: function( key, value ) {
		if ( key === "direction" ) {
			this._removeClass( "ui-controlgroup-" + this.options.direction );
		}

		this._super( key, value );
		if ( key === "disabled" ) {
			this._callChildMethod( value ? "disable" : "enable" );
			return;
		}

		this.refresh();
	},

	refresh: function() {
		var children,
			that = this;

		this._addClass( "ui-controlgroup ui-controlgroup-" + this.options.direction );

		if ( this.options.direction === "horizontal" ) {
			this._addClass( null, "ui-helper-clearfix" );
		}
		this._initWidgets();

		children = this.childWidgets;

		// We filter here because we need to track all childWidgets not just the visible ones
		if ( this.options.onlyVisible ) {
			children = children.filter( ":visible" );
		}

		if ( children.length ) {

			// We do this last because we need to make sure all enhancment is done
			// before determining first and last
			$.each( [ "first", "last" ], function( index, value ) {
				var instance = children[ value ]().data( "ui-controlgroup-data" );

				if ( instance && that[ "_" + instance.widgetName + "Options" ] ) {
					var options = that[ "_" + instance.widgetName + "Options" ](
						children.length === 1 ? "only" : value
					);
					options.classes = that._resolveClassesValues( options.classes, instance );
					instance.element[ instance.widgetName ]( options );
				} else {
					that._updateCornerClass( children[ value ](), value );
				}
			} );

			// Finally call the refresh method on each of the child widgets.
			this._callChildMethod( "refresh" );
		}
	}
} );

/*!
 * jQuery UI Checkboxradio 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Checkboxradio
//>>group: Widgets
//>>description: Enhances a form with multiple themeable checkboxes or radio buttons.
//>>docs: http://api.jqueryui.com/checkboxradio/
//>>demos: http://jqueryui.com/checkboxradio/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/button.css
//>>css.structure: ../../themes/base/checkboxradio.css
//>>css.theme: ../../themes/base/theme.css



$.widget( "ui.checkboxradio", [ $.ui.formResetMixin, {
	version: "1.12.1",
	options: {
		disabled: null,
		label: null,
		icon: true,
		classes: {
			"ui-checkboxradio-label": "ui-corner-all",
			"ui-checkboxradio-icon": "ui-corner-all"
		}
	},

	_getCreateOptions: function() {
		var disabled, labels;
		var that = this;
		var options = this._super() || {};

		// We read the type here, because it makes more sense to throw a element type error first,
		// rather then the error for lack of a label. Often if its the wrong type, it
		// won't have a label (e.g. calling on a div, btn, etc)
		this._readType();

		labels = this.element.labels();

		// If there are multiple labels, use the last one
		this.label = $( labels[ labels.length - 1 ] );
		if ( !this.label.length ) {
			$.error( "No label found for checkboxradio widget" );
		}

		this.originalLabel = "";

		// We need to get the label text but this may also need to make sure it does not contain the
		// input itself.
		this.label.contents().not( this.element[ 0 ] ).each( function() {

			// The label contents could be text, html, or a mix. We concat each element to get a
			// string representation of the label, without the input as part of it.
			that.originalLabel += this.nodeType === 3 ? $( this ).text() : this.outerHTML;
		} );

		// Set the label option if we found label text
		if ( this.originalLabel ) {
			options.label = this.originalLabel;
		}

		disabled = this.element[ 0 ].disabled;
		if ( disabled != null ) {
			options.disabled = disabled;
		}
		return options;
	},

	_create: function() {
		var checked = this.element[ 0 ].checked;

		this._bindFormResetHandler();

		if ( this.options.disabled == null ) {
			this.options.disabled = this.element[ 0 ].disabled;
		}

		this._setOption( "disabled", this.options.disabled );
		this._addClass( "ui-checkboxradio", "ui-helper-hidden-accessible" );
		this._addClass( this.label, "ui-checkboxradio-label", "ui-button ui-widget" );

		if ( this.type === "radio" ) {
			this._addClass( this.label, "ui-checkboxradio-radio-label" );
		}

		if ( this.options.label && this.options.label !== this.originalLabel ) {
			this._updateLabel();
		} else if ( this.originalLabel ) {
			this.options.label = this.originalLabel;
		}

		this._enhance();

		if ( checked ) {
			this._addClass( this.label, "ui-checkboxradio-checked", "ui-state-active" );
			if ( this.icon ) {
				this._addClass( this.icon, null, "ui-state-hover" );
			}
		}

		this._on( {
			change: "_toggleClasses",
			focus: function() {
				this._addClass( this.label, null, "ui-state-focus ui-visual-focus" );
			},
			blur: function() {
				this._removeClass( this.label, null, "ui-state-focus ui-visual-focus" );
			}
		} );
	},

	_readType: function() {
		var nodeName = this.element[ 0 ].nodeName.toLowerCase();
		this.type = this.element[ 0 ].type;
		if ( nodeName !== "input" || !/radio|checkbox/.test( this.type ) ) {
			$.error( "Can't create checkboxradio on element.nodeName=" + nodeName +
				" and element.type=" + this.type );
		}
	},

	// Support jQuery Mobile enhanced option
	_enhance: function() {
		this._updateIcon( this.element[ 0 ].checked );
	},

	widget: function() {
		return this.label;
	},

	_getRadioGroup: function() {
		var group;
		var name = this.element[ 0 ].name;
		var nameSelector = "input[name='" + $.ui.escapeSelector( name ) + "']";

		if ( !name ) {
			return $( [] );
		}

		if ( this.form.length ) {
			group = $( this.form[ 0 ].elements ).filter( nameSelector );
		} else {

			// Not inside a form, check all inputs that also are not inside a form
			group = $( nameSelector ).filter( function() {
				return $( this ).form().length === 0;
			} );
		}

		return group.not( this.element );
	},

	_toggleClasses: function() {
		var checked = this.element[ 0 ].checked;
		this._toggleClass( this.label, "ui-checkboxradio-checked", "ui-state-active", checked );

		if ( this.options.icon && this.type === "checkbox" ) {
			this._toggleClass( this.icon, null, "ui-icon-check ui-state-checked", checked )
				._toggleClass( this.icon, null, "ui-icon-blank", !checked );
		}

		if ( this.type === "radio" ) {
			this._getRadioGroup()
				.each( function() {
					var instance = $( this ).checkboxradio( "instance" );

					if ( instance ) {
						instance._removeClass( instance.label,
							"ui-checkboxradio-checked", "ui-state-active" );
					}
				} );
		}
	},

	_destroy: function() {
		this._unbindFormResetHandler();

		if ( this.icon ) {
			this.icon.remove();
			this.iconSpace.remove();
		}
	},

	_setOption: function( key, value ) {

		// We don't allow the value to be set to nothing
		if ( key === "label" && !value ) {
			return;
		}

		this._super( key, value );

		if ( key === "disabled" ) {
			this._toggleClass( this.label, null, "ui-state-disabled", value );
			this.element[ 0 ].disabled = value;

			// Don't refresh when setting disabled
			return;
		}
		this.refresh();
	},

	_updateIcon: function( checked ) {
		var toAdd = "ui-icon ui-icon-background ";

		if ( this.options.icon ) {
			if ( !this.icon ) {
				this.icon = $( "<span>" );
				this.iconSpace = $( "<span> </span>" );
				this._addClass( this.iconSpace, "ui-checkboxradio-icon-space" );
			}

			if ( this.type === "checkbox" ) {
				toAdd += checked ? "ui-icon-check ui-state-checked" : "ui-icon-blank";
				this._removeClass( this.icon, null, checked ? "ui-icon-blank" : "ui-icon-check" );
			} else {
				toAdd += "ui-icon-blank";
			}
			this._addClass( this.icon, "ui-checkboxradio-icon", toAdd );
			if ( !checked ) {
				this._removeClass( this.icon, null, "ui-icon-check ui-state-checked" );
			}
			this.icon.prependTo( this.label ).after( this.iconSpace );
		} else if ( this.icon !== undefined ) {
			this.icon.remove();
			this.iconSpace.remove();
			delete this.icon;
		}
	},

	_updateLabel: function() {

		// Remove the contents of the label ( minus the icon, icon space, and input )
		var contents = this.label.contents().not( this.element[ 0 ] );
		if ( this.icon ) {
			contents = contents.not( this.icon[ 0 ] );
		}
		if ( this.iconSpace ) {
			contents = contents.not( this.iconSpace[ 0 ] );
		}
		contents.remove();

		this.label.append( this.options.label );
	},

	refresh: function() {
		var checked = this.element[ 0 ].checked,
			isDisabled = this.element[ 0 ].disabled;

		this._updateIcon( checked );
		this._toggleClass( this.label, "ui-checkboxradio-checked", "ui-state-active", checked );
		if ( this.options.label !== null ) {
			this._updateLabel();
		}

		if ( isDisabled !== this.options.disabled ) {
			this._setOptions( { "disabled": isDisabled } );
		}
	}

} ] );

var widgetsCheckboxradio = $.ui.checkboxradio;


/*!
 * jQuery UI Button 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Button
//>>group: Widgets
//>>description: Enhances a form with themeable buttons.
//>>docs: http://api.jqueryui.com/button/
//>>demos: http://jqueryui.com/button/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/button.css
//>>css.theme: ../../themes/base/theme.css



$.widget( "ui.button", {
	version: "1.12.1",
	defaultElement: "<button>",
	options: {
		classes: {
			"ui-button": "ui-corner-all"
		},
		disabled: null,
		icon: null,
		iconPosition: "beginning",
		label: null,
		showLabel: true
	},

	_getCreateOptions: function() {
		var disabled,

			// This is to support cases like in jQuery Mobile where the base widget does have
			// an implementation of _getCreateOptions
			options = this._super() || {};

		this.isInput = this.element.is( "input" );

		disabled = this.element[ 0 ].disabled;
		if ( disabled != null ) {
			options.disabled = disabled;
		}

		this.originalLabel = this.isInput ? this.element.val() : this.element.html();
		if ( this.originalLabel ) {
			options.label = this.originalLabel;
		}

		return options;
	},

	_create: function() {
		if ( !this.option.showLabel & !this.options.icon ) {
			this.options.showLabel = true;
		}

		// We have to check the option again here even though we did in _getCreateOptions,
		// because null may have been passed on init which would override what was set in
		// _getCreateOptions
		if ( this.options.disabled == null ) {
			this.options.disabled = this.element[ 0 ].disabled || false;
		}

		this.hasTitle = !!this.element.attr( "title" );

		// Check to see if the label needs to be set or if its already correct
		if ( this.options.label && this.options.label !== this.originalLabel ) {
			if ( this.isInput ) {
				this.element.val( this.options.label );
			} else {
				this.element.html( this.options.label );
			}
		}
		this._addClass( "ui-button", "ui-widget" );
		this._setOption( "disabled", this.options.disabled );
		this._enhance();

		if ( this.element.is( "a" ) ) {
			this._on( {
				"keyup": function( event ) {
					if ( event.keyCode === $.ui.keyCode.SPACE ) {
						event.preventDefault();

						// Support: PhantomJS <= 1.9, IE 8 Only
						// If a native click is available use it so we actually cause navigation
						// otherwise just trigger a click event
						if ( this.element[ 0 ].click ) {
							this.element[ 0 ].click();
						} else {
							this.element.trigger( "click" );
						}
					}
				}
			} );
		}
	},

	_enhance: function() {
		if ( !this.element.is( "button" ) ) {
			this.element.attr( "role", "button" );
		}

		if ( this.options.icon ) {
			this._updateIcon( "icon", this.options.icon );
			this._updateTooltip();
		}
	},

	_updateTooltip: function() {
		this.title = this.element.attr( "title" );

		if ( !this.options.showLabel && !this.title ) {
			this.element.attr( "title", this.options.label );
		}
	},

	_updateIcon: function( option, value ) {
		var icon = option !== "iconPosition",
			position = icon ? this.options.iconPosition : value,
			displayBlock = position === "top" || position === "bottom";

		// Create icon
		if ( !this.icon ) {
			this.icon = $( "<span>" );

			this._addClass( this.icon, "ui-button-icon", "ui-icon" );

			if ( !this.options.showLabel ) {
				this._addClass( "ui-button-icon-only" );
			}
		} else if ( icon ) {

			// If we are updating the icon remove the old icon class
			this._removeClass( this.icon, null, this.options.icon );
		}

		// If we are updating the icon add the new icon class
		if ( icon ) {
			this._addClass( this.icon, null, value );
		}

		this._attachIcon( position );

		// If the icon is on top or bottom we need to add the ui-widget-icon-block class and remove
		// the iconSpace if there is one.
		if ( displayBlock ) {
			this._addClass( this.icon, null, "ui-widget-icon-block" );
			if ( this.iconSpace ) {
				this.iconSpace.remove();
			}
		} else {

			// Position is beginning or end so remove the ui-widget-icon-block class and add the
			// space if it does not exist
			if ( !this.iconSpace ) {
				this.iconSpace = $( "<span> </span>" );
				this._addClass( this.iconSpace, "ui-button-icon-space" );
			}
			this._removeClass( this.icon, null, "ui-wiget-icon-block" );
			this._attachIconSpace( position );
		}
	},

	_destroy: function() {
		this.element.removeAttr( "role" );

		if ( this.icon ) {
			this.icon.remove();
		}
		if ( this.iconSpace ) {
			this.iconSpace.remove();
		}
		if ( !this.hasTitle ) {
			this.element.removeAttr( "title" );
		}
	},

	_attachIconSpace: function( iconPosition ) {
		this.icon[ /^(?:end|bottom)/.test( iconPosition ) ? "before" : "after" ]( this.iconSpace );
	},

	_attachIcon: function( iconPosition ) {
		this.element[ /^(?:end|bottom)/.test( iconPosition ) ? "append" : "prepend" ]( this.icon );
	},

	_setOptions: function( options ) {
		var newShowLabel = options.showLabel === undefined ?
				this.options.showLabel :
				options.showLabel,
			newIcon = options.icon === undefined ? this.options.icon : options.icon;

		if ( !newShowLabel && !newIcon ) {
			options.showLabel = true;
		}
		this._super( options );
	},

	_setOption: function( key, value ) {
		if ( key === "icon" ) {
			if ( value ) {
				this._updateIcon( key, value );
			} else if ( this.icon ) {
				this.icon.remove();
				if ( this.iconSpace ) {
					this.iconSpace.remove();
				}
			}
		}

		if ( key === "iconPosition" ) {
			this._updateIcon( key, value );
		}

		// Make sure we can't end up with a button that has neither text nor icon
		if ( key === "showLabel" ) {
				this._toggleClass( "ui-button-icon-only", null, !value );
				this._updateTooltip();
		}

		if ( key === "label" ) {
			if ( this.isInput ) {
				this.element.val( value );
			} else {

				// If there is an icon, append it, else nothing then append the value
				// this avoids removal of the icon when setting label text
				this.element.html( value );
				if ( this.icon ) {
					this._attachIcon( this.options.iconPosition );
					this._attachIconSpace( this.options.iconPosition );
				}
			}
		}

		this._super( key, value );

		if ( key === "disabled" ) {
			this._toggleClass( null, "ui-state-disabled", value );
			this.element[ 0 ].disabled = value;
			if ( value ) {
				this.element.blur();
			}
		}
	},

	refresh: function() {

		// Make sure to only check disabled if its an element that supports this otherwise
		// check for the disabled class to determine state
		var isDisabled = this.element.is( "input, button" ) ?
			this.element[ 0 ].disabled : this.element.hasClass( "ui-button-disabled" );

		if ( isDisabled !== this.options.disabled ) {
			this._setOptions( { disabled: isDisabled } );
		}

		this._updateTooltip();
	}
} );

// DEPRECATED
if ( $.uiBackCompat !== false ) {

	// Text and Icons options
	$.widget( "ui.button", $.ui.button, {
		options: {
			text: true,
			icons: {
				primary: null,
				secondary: null
			}
		},

		_create: function() {
			if ( this.options.showLabel && !this.options.text ) {
				this.options.showLabel = this.options.text;
			}
			if ( !this.options.showLabel && this.options.text ) {
				this.options.text = this.options.showLabel;
			}
			if ( !this.options.icon && ( this.options.icons.primary ||
					this.options.icons.secondary ) ) {
				if ( this.options.icons.primary ) {
					this.options.icon = this.options.icons.primary;
				} else {
					this.options.icon = this.options.icons.secondary;
					this.options.iconPosition = "end";
				}
			} else if ( this.options.icon ) {
				this.options.icons.primary = this.options.icon;
			}
			this._super();
		},

		_setOption: function( key, value ) {
			if ( key === "text" ) {
				this._super( "showLabel", value );
				return;
			}
			if ( key === "showLabel" ) {
				this.options.text = value;
			}
			if ( key === "icon" ) {
				this.options.icons.primary = value;
			}
			if ( key === "icons" ) {
				if ( value.primary ) {
					this._super( "icon", value.primary );
					this._super( "iconPosition", "beginning" );
				} else if ( value.secondary ) {
					this._super( "icon", value.secondary );
					this._super( "iconPosition", "end" );
				}
			}
			this._superApply( arguments );
		}
	} );

	$.fn.button = ( function( orig ) {
		return function() {
			if ( !this.length || ( this.length && this[ 0 ].tagName !== "INPUT" ) ||
					( this.length && this[ 0 ].tagName === "INPUT" && (
						this.attr( "type" ) !== "checkbox" && this.attr( "type" ) !== "radio"
					) ) ) {
				return orig.apply( this, arguments );
			}
			if ( !$.ui.checkboxradio ) {
				$.error( "Checkboxradio widget missing" );
			}
			if ( arguments.length === 0 ) {
				return this.checkboxradio( {
					"icon": false
				} );
			}
			return this.checkboxradio.apply( this, arguments );
		};
	} )( $.fn.button );

	$.fn.buttonset = function() {
		if ( !$.ui.controlgroup ) {
			$.error( "Controlgroup widget missing" );
		}
		if ( arguments[ 0 ] === "option" && arguments[ 1 ] === "items" && arguments[ 2 ] ) {
			return this.controlgroup.apply( this,
				[ arguments[ 0 ], "items.button", arguments[ 2 ] ] );
		}
		if ( arguments[ 0 ] === "option" && arguments[ 1 ] === "items" ) {
			return this.controlgroup.apply( this, [ arguments[ 0 ], "items.button" ] );
		}
		if ( typeof arguments[ 0 ] === "object" && arguments[ 0 ].items ) {
			arguments[ 0 ].items = {
				button: arguments[ 0 ].items
			};
		}
		return this.controlgroup.apply( this, arguments );
	};
}

var widgetsButton = $.ui.button;


// jscs:disable maximumLineLength
/* jscs:disable requireCamelCaseOrUpperCaseIdentifiers */
/*!
 * jQuery UI Datepicker 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Datepicker
//>>group: Widgets
//>>description: Displays a calendar from an input or inline for selecting dates.
//>>docs: http://api.jqueryui.com/datepicker/
//>>demos: http://jqueryui.com/datepicker/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/datepicker.css
//>>css.theme: ../../themes/base/theme.css



$.extend( $.ui, { datepicker: { version: "1.12.1" } } );

var datepicker_instActive;

function datepicker_getZindex( elem ) {
	var position, value;
	while ( elem.length && elem[ 0 ] !== document ) {

		// Ignore z-index if position is set to a value where z-index is ignored by the browser
		// This makes behavior of this function consistent across browsers
		// WebKit always returns auto if the element is positioned
		position = elem.css( "position" );
		if ( position === "absolute" || position === "relative" || position === "fixed" ) {

			// IE returns 0 when zIndex is not specified
			// other browsers return a string
			// we ignore the case of nested elements with an explicit value of 0
			// <div style="z-index: -10;"><div style="z-index: 0;"></div></div>
			value = parseInt( elem.css( "zIndex" ), 10 );
			if ( !isNaN( value ) && value !== 0 ) {
				return value;
			}
		}
		elem = elem.parent();
	}

	return 0;
}
/* Date picker manager.
   Use the singleton instance of this class, $.datepicker, to interact with the date picker.
   Settings for (groups of) date pickers are maintained in an instance object,
   allowing multiple different settings on the same page. */

function Datepicker() {
	this._curInst = null; // The current instance in use
	this._keyEvent = false; // If the last event was a key event
	this._disabledInputs = []; // List of date picker inputs that have been disabled
	this._datepickerShowing = false; // True if the popup picker is showing , false if not
	this._inDialog = false; // True if showing within a "dialog", false if not
	this._mainDivId = "ui-datepicker-div"; // The ID of the main datepicker division
	this._inlineClass = "ui-datepicker-inline"; // The name of the inline marker class
	this._appendClass = "ui-datepicker-append"; // The name of the append marker class
	this._triggerClass = "ui-datepicker-trigger"; // The name of the trigger marker class
	this._dialogClass = "ui-datepicker-dialog"; // The name of the dialog marker class
	this._disableClass = "ui-datepicker-disabled"; // The name of the disabled covering marker class
	this._unselectableClass = "ui-datepicker-unselectable"; // The name of the unselectable cell marker class
	this._currentClass = "ui-datepicker-current-day"; // The name of the current day marker class
	this._dayOverClass = "ui-datepicker-days-cell-over"; // The name of the day hover marker class
	this.regional = []; // Available regional settings, indexed by language code
	this.regional[ "" ] = { // Default regional settings
		closeText: "Done", // Display text for close link
		prevText: "Prev", // Display text for previous month link
		nextText: "Next", // Display text for next month link
		currentText: "Today", // Display text for current month link
		monthNames: [ "January","February","March","April","May","June",
			"July","August","September","October","November","December" ], // Names of months for drop-down and formatting
		monthNamesShort: [ "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec" ], // For formatting
		dayNames: [ "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday" ], // For formatting
		dayNamesShort: [ "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat" ], // For formatting
		dayNamesMin: [ "Su","Mo","Tu","We","Th","Fr","Sa" ], // Column headings for days starting at Sunday
		weekHeader: "Wk", // Column header for week of the year
		dateFormat: "mm/dd/yy", // See format options on parseDate
		firstDay: 0, // The first day of the week, Sun = 0, Mon = 1, ...
		isRTL: false, // True if right-to-left language, false if left-to-right
		showMonthAfterYear: false, // True if the year select precedes month, false for month then year
		yearSuffix: "" // Additional text to append to the year in the month headers
	};
	this._defaults = { // Global defaults for all the date picker instances
		showOn: "focus", // "focus" for popup on focus,
			// "button" for trigger button, or "both" for either
		showAnim: "fadeIn", // Name of jQuery animation for popup
		showOptions: {}, // Options for enhanced animations
		defaultDate: null, // Used when field is blank: actual date,
			// +/-number for offset from today, null for today
		appendText: "", // Display text following the input box, e.g. showing the format
		buttonText: "...", // Text for trigger button
		buttonImage: "", // URL for trigger button image
		buttonImageOnly: false, // True if the image appears alone, false if it appears on a button
		hideIfNoPrevNext: false, // True to hide next/previous month links
			// if not applicable, false to just disable them
		navigationAsDateFormat: false, // True if date formatting applied to prev/today/next links
		gotoCurrent: false, // True if today link goes back to current selection instead
		changeMonth: false, // True if month can be selected directly, false if only prev/next
		changeYear: false, // True if year can be selected directly, false if only prev/next
		yearRange: "c-10:c+10", // Range of years to display in drop-down,
			// either relative to today's year (-nn:+nn), relative to currently displayed year
			// (c-nn:c+nn), absolute (nnnn:nnnn), or a combination of the above (nnnn:-n)
		showOtherMonths: false, // True to show dates in other months, false to leave blank
		selectOtherMonths: false, // True to allow selection of dates in other months, false for unselectable
		showWeek: false, // True to show week of the year, false to not show it
		calculateWeek: this.iso8601Week, // How to calculate the week of the year,
			// takes a Date and returns the number of the week for it
		shortYearCutoff: "+10", // Short year values < this are in the current century,
			// > this are in the previous century,
			// string value starting with "+" for current year + value
		minDate: null, // The earliest selectable date, or null for no limit
		maxDate: null, // The latest selectable date, or null for no limit
		duration: "fast", // Duration of display/closure
		beforeShowDay: null, // Function that takes a date and returns an array with
			// [0] = true if selectable, false if not, [1] = custom CSS class name(s) or "",
			// [2] = cell title (optional), e.g. $.datepicker.noWeekends
		beforeShow: null, // Function that takes an input field and
			// returns a set of custom settings for the date picker
		onSelect: null, // Define a callback function when a date is selected
		onChangeMonthYear: null, // Define a callback function when the month or year is changed
		onClose: null, // Define a callback function when the datepicker is closed
		numberOfMonths: 1, // Number of months to show at a time
		showCurrentAtPos: 0, // The position in multipe months at which to show the current month (starting at 0)
		stepMonths: 1, // Number of months to step back/forward
		stepBigMonths: 12, // Number of months to step back/forward for the big links
		altField: "", // Selector for an alternate field to store selected dates into
		altFormat: "", // The date format to use for the alternate field
		constrainInput: true, // The input is constrained by the current date format
		showButtonPanel: false, // True to show button panel, false to not show it
		autoSize: false, // True to size the input for the date format, false to leave as is
		disabled: false // The initial disabled state
	};
	$.extend( this._defaults, this.regional[ "" ] );
	this.regional.en = $.extend( true, {}, this.regional[ "" ] );
	this.regional[ "en-US" ] = $.extend( true, {}, this.regional.en );
	this.dpDiv = datepicker_bindHover( $( "<div id='" + this._mainDivId + "' class='ui-datepicker ui-widget ui-widget-content ui-helper-clearfix ui-corner-all'></div>" ) );
}

$.extend( Datepicker.prototype, {
	/* Class name added to elements to indicate already configured with a date picker. */
	markerClassName: "hasDatepicker",

	//Keep track of the maximum number of rows displayed (see #7043)
	maxRows: 4,

	// TODO rename to "widget" when switching to widget factory
	_widgetDatepicker: function() {
		return this.dpDiv;
	},

	/* Override the default settings for all instances of the date picker.
	 * @param  settings  object - the new settings to use as defaults (anonymous object)
	 * @return the manager object
	 */
	setDefaults: function( settings ) {
		datepicker_extendRemove( this._defaults, settings || {} );
		return this;
	},

	/* Attach the date picker to a jQuery selection.
	 * @param  target	element - the target input field or division or span
	 * @param  settings  object - the new settings to use for this date picker instance (anonymous)
	 */
	_attachDatepicker: function( target, settings ) {
		var nodeName, inline, inst;
		nodeName = target.nodeName.toLowerCase();
		inline = ( nodeName === "div" || nodeName === "span" );
		if ( !target.id ) {
			this.uuid += 1;
			target.id = "dp" + this.uuid;
		}
		inst = this._newInst( $( target ), inline );
		inst.settings = $.extend( {}, settings || {} );
		if ( nodeName === "input" ) {
			this._connectDatepicker( target, inst );
		} else if ( inline ) {
			this._inlineDatepicker( target, inst );
		}
	},

	/* Create a new instance object. */
	_newInst: function( target, inline ) {
		var id = target[ 0 ].id.replace( /([^A-Za-z0-9_\-])/g, "\\\\$1" ); // escape jQuery meta chars
		return { id: id, input: target, // associated target
			selectedDay: 0, selectedMonth: 0, selectedYear: 0, // current selection
			drawMonth: 0, drawYear: 0, // month being drawn
			inline: inline, // is datepicker inline or not
			dpDiv: ( !inline ? this.dpDiv : // presentation div
			datepicker_bindHover( $( "<div class='" + this._inlineClass + " ui-datepicker ui-widget ui-widget-content ui-helper-clearfix ui-corner-all'></div>" ) ) ) };
	},

	/* Attach the date picker to an input field. */
	_connectDatepicker: function( target, inst ) {
		var input = $( target );
		inst.append = $( [] );
		inst.trigger = $( [] );
		if ( input.hasClass( this.markerClassName ) ) {
			return;
		}
		this._attachments( input, inst );
		input.addClass( this.markerClassName ).on( "keydown", this._doKeyDown ).
			on( "keypress", this._doKeyPress ).on( "keyup", this._doKeyUp );
		this._autoSize( inst );
		$.data( target, "datepicker", inst );

		//If disabled option is true, disable the datepicker once it has been attached to the input (see ticket #5665)
		if ( inst.settings.disabled ) {
			this._disableDatepicker( target );
		}
	},

	/* Make attachments based on settings. */
	_attachments: function( input, inst ) {
		var showOn, buttonText, buttonImage,
			appendText = this._get( inst, "appendText" ),
			isRTL = this._get( inst, "isRTL" );

		if ( inst.append ) {
			inst.append.remove();
		}
		if ( appendText ) {
			inst.append = $( "<span class='" + this._appendClass + "'>" + appendText + "</span>" );
			input[ isRTL ? "before" : "after" ]( inst.append );
		}

		input.off( "focus", this._showDatepicker );

		if ( inst.trigger ) {
			inst.trigger.remove();
		}

		showOn = this._get( inst, "showOn" );
		if ( showOn === "focus" || showOn === "both" ) { // pop-up date picker when in the marked field
			input.on( "focus", this._showDatepicker );
		}
		if ( showOn === "button" || showOn === "both" ) { // pop-up date picker when button clicked
			buttonText = this._get( inst, "buttonText" );
			buttonImage = this._get( inst, "buttonImage" );
			inst.trigger = $( this._get( inst, "buttonImageOnly" ) ?
				$( "<img/>" ).addClass( this._triggerClass ).
					attr( { src: buttonImage, alt: buttonText, title: buttonText } ) :
				$( "<button type='button'></button>" ).addClass( this._triggerClass ).
					html( !buttonImage ? buttonText : $( "<img/>" ).attr(
					{ src:buttonImage, alt:buttonText, title:buttonText } ) ) );
			input[ isRTL ? "before" : "after" ]( inst.trigger );
			inst.trigger.on( "click", function() {
				if ( $.datepicker._datepickerShowing && $.datepicker._lastInput === input[ 0 ] ) {
					$.datepicker._hideDatepicker();
				} else if ( $.datepicker._datepickerShowing && $.datepicker._lastInput !== input[ 0 ] ) {
					$.datepicker._hideDatepicker();
					$.datepicker._showDatepicker( input[ 0 ] );
				} else {
					$.datepicker._showDatepicker( input[ 0 ] );
				}
				return false;
			} );
		}
	},

	/* Apply the maximum length for the date format. */
	_autoSize: function( inst ) {
		if ( this._get( inst, "autoSize" ) && !inst.inline ) {
			var findMax, max, maxI, i,
				date = new Date( 2009, 12 - 1, 20 ), // Ensure double digits
				dateFormat = this._get( inst, "dateFormat" );

			if ( dateFormat.match( /[DM]/ ) ) {
				findMax = function( names ) {
					max = 0;
					maxI = 0;
					for ( i = 0; i < names.length; i++ ) {
						if ( names[ i ].length > max ) {
							max = names[ i ].length;
							maxI = i;
						}
					}
					return maxI;
				};
				date.setMonth( findMax( this._get( inst, ( dateFormat.match( /MM/ ) ?
					"monthNames" : "monthNamesShort" ) ) ) );
				date.setDate( findMax( this._get( inst, ( dateFormat.match( /DD/ ) ?
					"dayNames" : "dayNamesShort" ) ) ) + 20 - date.getDay() );
			}
			inst.input.attr( "size", this._formatDate( inst, date ).length );
		}
	},

	/* Attach an inline date picker to a div. */
	_inlineDatepicker: function( target, inst ) {
		var divSpan = $( target );
		if ( divSpan.hasClass( this.markerClassName ) ) {
			return;
		}
		divSpan.addClass( this.markerClassName ).append( inst.dpDiv );
		$.data( target, "datepicker", inst );
		this._setDate( inst, this._getDefaultDate( inst ), true );
		this._updateDatepicker( inst );
		this._updateAlternate( inst );

		//If disabled option is true, disable the datepicker before showing it (see ticket #5665)
		if ( inst.settings.disabled ) {
			this._disableDatepicker( target );
		}

		// Set display:block in place of inst.dpDiv.show() which won't work on disconnected elements
		// http://bugs.jqueryui.com/ticket/7552 - A Datepicker created on a detached div has zero height
		inst.dpDiv.css( "display", "block" );
	},

	/* Pop-up the date picker in a "dialog" box.
	 * @param  input element - ignored
	 * @param  date	string or Date - the initial date to display
	 * @param  onSelect  function - the function to call when a date is selected
	 * @param  settings  object - update the dialog date picker instance's settings (anonymous object)
	 * @param  pos int[2] - coordinates for the dialog's position within the screen or
	 *					event - with x/y coordinates or
	 *					leave empty for default (screen centre)
	 * @return the manager object
	 */
	_dialogDatepicker: function( input, date, onSelect, settings, pos ) {
		var id, browserWidth, browserHeight, scrollX, scrollY,
			inst = this._dialogInst; // internal instance

		if ( !inst ) {
			this.uuid += 1;
			id = "dp" + this.uuid;
			this._dialogInput = $( "<input type='text' id='" + id +
				"' style='position: absolute; top: -100px; width: 0px;'/>" );
			this._dialogInput.on( "keydown", this._doKeyDown );
			$( "body" ).append( this._dialogInput );
			inst = this._dialogInst = this._newInst( this._dialogInput, false );
			inst.settings = {};
			$.data( this._dialogInput[ 0 ], "datepicker", inst );
		}
		datepicker_extendRemove( inst.settings, settings || {} );
		date = ( date && date.constructor === Date ? this._formatDate( inst, date ) : date );
		this._dialogInput.val( date );

		this._pos = ( pos ? ( pos.length ? pos : [ pos.pageX, pos.pageY ] ) : null );
		if ( !this._pos ) {
			browserWidth = document.documentElement.clientWidth;
			browserHeight = document.documentElement.clientHeight;
			scrollX = document.documentElement.scrollLeft || document.body.scrollLeft;
			scrollY = document.documentElement.scrollTop || document.body.scrollTop;
			this._pos = // should use actual width/height below
				[ ( browserWidth / 2 ) - 100 + scrollX, ( browserHeight / 2 ) - 150 + scrollY ];
		}

		// Move input on screen for focus, but hidden behind dialog
		this._dialogInput.css( "left", ( this._pos[ 0 ] + 20 ) + "px" ).css( "top", this._pos[ 1 ] + "px" );
		inst.settings.onSelect = onSelect;
		this._inDialog = true;
		this.dpDiv.addClass( this._dialogClass );
		this._showDatepicker( this._dialogInput[ 0 ] );
		if ( $.blockUI ) {
			$.blockUI( this.dpDiv );
		}
		$.data( this._dialogInput[ 0 ], "datepicker", inst );
		return this;
	},

	/* Detach a datepicker from its control.
	 * @param  target	element - the target input field or division or span
	 */
	_destroyDatepicker: function( target ) {
		var nodeName,
			$target = $( target ),
			inst = $.data( target, "datepicker" );

		if ( !$target.hasClass( this.markerClassName ) ) {
			return;
		}

		nodeName = target.nodeName.toLowerCase();
		$.removeData( target, "datepicker" );
		if ( nodeName === "input" ) {
			inst.append.remove();
			inst.trigger.remove();
			$target.removeClass( this.markerClassName ).
				off( "focus", this._showDatepicker ).
				off( "keydown", this._doKeyDown ).
				off( "keypress", this._doKeyPress ).
				off( "keyup", this._doKeyUp );
		} else if ( nodeName === "div" || nodeName === "span" ) {
			$target.removeClass( this.markerClassName ).empty();
		}

		if ( datepicker_instActive === inst ) {
			datepicker_instActive = null;
		}
	},

	/* Enable the date picker to a jQuery selection.
	 * @param  target	element - the target input field or division or span
	 */
	_enableDatepicker: function( target ) {
		var nodeName, inline,
			$target = $( target ),
			inst = $.data( target, "datepicker" );

		if ( !$target.hasClass( this.markerClassName ) ) {
			return;
		}

		nodeName = target.nodeName.toLowerCase();
		if ( nodeName === "input" ) {
			target.disabled = false;
			inst.trigger.filter( "button" ).
				each( function() { this.disabled = false; } ).end().
				filter( "img" ).css( { opacity: "1.0", cursor: "" } );
		} else if ( nodeName === "div" || nodeName === "span" ) {
			inline = $target.children( "." + this._inlineClass );
			inline.children().removeClass( "ui-state-disabled" );
			inline.find( "select.ui-datepicker-month, select.ui-datepicker-year" ).
				prop( "disabled", false );
		}
		this._disabledInputs = $.map( this._disabledInputs,
			function( value ) { return ( value === target ? null : value ); } ); // delete entry
	},

	/* Disable the date picker to a jQuery selection.
	 * @param  target	element - the target input field or division or span
	 */
	_disableDatepicker: function( target ) {
		var nodeName, inline,
			$target = $( target ),
			inst = $.data( target, "datepicker" );

		if ( !$target.hasClass( this.markerClassName ) ) {
			return;
		}

		nodeName = target.nodeName.toLowerCase();
		if ( nodeName === "input" ) {
			target.disabled = true;
			inst.trigger.filter( "button" ).
				each( function() { this.disabled = true; } ).end().
				filter( "img" ).css( { opacity: "0.5", cursor: "default" } );
		} else if ( nodeName === "div" || nodeName === "span" ) {
			inline = $target.children( "." + this._inlineClass );
			inline.children().addClass( "ui-state-disabled" );
			inline.find( "select.ui-datepicker-month, select.ui-datepicker-year" ).
				prop( "disabled", true );
		}
		this._disabledInputs = $.map( this._disabledInputs,
			function( value ) { return ( value === target ? null : value ); } ); // delete entry
		this._disabledInputs[ this._disabledInputs.length ] = target;
	},

	/* Is the first field in a jQuery collection disabled as a datepicker?
	 * @param  target	element - the target input field or division or span
	 * @return boolean - true if disabled, false if enabled
	 */
	_isDisabledDatepicker: function( target ) {
		if ( !target ) {
			return false;
		}
		for ( var i = 0; i < this._disabledInputs.length; i++ ) {
			if ( this._disabledInputs[ i ] === target ) {
				return true;
			}
		}
		return false;
	},

	/* Retrieve the instance data for the target control.
	 * @param  target  element - the target input field or division or span
	 * @return  object - the associated instance data
	 * @throws  error if a jQuery problem getting data
	 */
	_getInst: function( target ) {
		try {
			return $.data( target, "datepicker" );
		}
		catch ( err ) {
			throw "Missing instance data for this datepicker";
		}
	},

	/* Update or retrieve the settings for a date picker attached to an input field or division.
	 * @param  target  element - the target input field or division or span
	 * @param  name	object - the new settings to update or
	 *				string - the name of the setting to change or retrieve,
	 *				when retrieving also "all" for all instance settings or
	 *				"defaults" for all global defaults
	 * @param  value   any - the new value for the setting
	 *				(omit if above is an object or to retrieve a value)
	 */
	_optionDatepicker: function( target, name, value ) {
		var settings, date, minDate, maxDate,
			inst = this._getInst( target );

		if ( arguments.length === 2 && typeof name === "string" ) {
			return ( name === "defaults" ? $.extend( {}, $.datepicker._defaults ) :
				( inst ? ( name === "all" ? $.extend( {}, inst.settings ) :
				this._get( inst, name ) ) : null ) );
		}

		settings = name || {};
		if ( typeof name === "string" ) {
			settings = {};
			settings[ name ] = value;
		}

		if ( inst ) {
			if ( this._curInst === inst ) {
				this._hideDatepicker();
			}

			date = this._getDateDatepicker( target, true );
			minDate = this._getMinMaxDate( inst, "min" );
			maxDate = this._getMinMaxDate( inst, "max" );
			datepicker_extendRemove( inst.settings, settings );

			// reformat the old minDate/maxDate values if dateFormat changes and a new minDate/maxDate isn't provided
			if ( minDate !== null && settings.dateFormat !== undefined && settings.minDate === undefined ) {
				inst.settings.minDate = this._formatDate( inst, minDate );
			}
			if ( maxDate !== null && settings.dateFormat !== undefined && settings.maxDate === undefined ) {
				inst.settings.maxDate = this._formatDate( inst, maxDate );
			}
			if ( "disabled" in settings ) {
				if ( settings.disabled ) {
					this._disableDatepicker( target );
				} else {
					this._enableDatepicker( target );
				}
			}
			this._attachments( $( target ), inst );
			this._autoSize( inst );
			this._setDate( inst, date );
			this._updateAlternate( inst );
			this._updateDatepicker( inst );
		}
	},

	// Change method deprecated
	_changeDatepicker: function( target, name, value ) {
		this._optionDatepicker( target, name, value );
	},

	/* Redraw the date picker attached to an input field or division.
	 * @param  target  element - the target input field or division or span
	 */
	_refreshDatepicker: function( target ) {
		var inst = this._getInst( target );
		if ( inst ) {
			this._updateDatepicker( inst );
		}
	},

	/* Set the dates for a jQuery selection.
	 * @param  target element - the target input field or division or span
	 * @param  date	Date - the new date
	 */
	_setDateDatepicker: function( target, date ) {
		var inst = this._getInst( target );
		if ( inst ) {
			this._setDate( inst, date );
			this._updateDatepicker( inst );
			this._updateAlternate( inst );
		}
	},

	/* Get the date(s) for the first entry in a jQuery selection.
	 * @param  target element - the target input field or division or span
	 * @param  noDefault boolean - true if no default date is to be used
	 * @return Date - the current date
	 */
	_getDateDatepicker: function( target, noDefault ) {
		var inst = this._getInst( target );
		if ( inst && !inst.inline ) {
			this._setDateFromField( inst, noDefault );
		}
		return ( inst ? this._getDate( inst ) : null );
	},

	/* Handle keystrokes. */
	_doKeyDown: function( event ) {
		var onSelect, dateStr, sel,
			inst = $.datepicker._getInst( event.target ),
			handled = true,
			isRTL = inst.dpDiv.is( ".ui-datepicker-rtl" );

		inst._keyEvent = true;
		if ( $.datepicker._datepickerShowing ) {
			switch ( event.keyCode ) {
				case 9: $.datepicker._hideDatepicker();
						handled = false;
						break; // hide on tab out
				case 13: sel = $( "td." + $.datepicker._dayOverClass + ":not(." +
									$.datepicker._currentClass + ")", inst.dpDiv );
						if ( sel[ 0 ] ) {
							$.datepicker._selectDay( event.target, inst.selectedMonth, inst.selectedYear, sel[ 0 ] );
						}

						onSelect = $.datepicker._get( inst, "onSelect" );
						if ( onSelect ) {
							dateStr = $.datepicker._formatDate( inst );

							// Trigger custom callback
							onSelect.apply( ( inst.input ? inst.input[ 0 ] : null ), [ dateStr, inst ] );
						} else {
							$.datepicker._hideDatepicker();
						}

						return false; // don't submit the form
				case 27: $.datepicker._hideDatepicker();
						break; // hide on escape
				case 33: $.datepicker._adjustDate( event.target, ( event.ctrlKey ?
							-$.datepicker._get( inst, "stepBigMonths" ) :
							-$.datepicker._get( inst, "stepMonths" ) ), "M" );
						break; // previous month/year on page up/+ ctrl
				case 34: $.datepicker._adjustDate( event.target, ( event.ctrlKey ?
							+$.datepicker._get( inst, "stepBigMonths" ) :
							+$.datepicker._get( inst, "stepMonths" ) ), "M" );
						break; // next month/year on page down/+ ctrl
				case 35: if ( event.ctrlKey || event.metaKey ) {
							$.datepicker._clearDate( event.target );
						}
						handled = event.ctrlKey || event.metaKey;
						break; // clear on ctrl or command +end
				case 36: if ( event.ctrlKey || event.metaKey ) {
							$.datepicker._gotoToday( event.target );
						}
						handled = event.ctrlKey || event.metaKey;
						break; // current on ctrl or command +home
				case 37: if ( event.ctrlKey || event.metaKey ) {
							$.datepicker._adjustDate( event.target, ( isRTL ? +1 : -1 ), "D" );
						}
						handled = event.ctrlKey || event.metaKey;

						// -1 day on ctrl or command +left
						if ( event.originalEvent.altKey ) {
							$.datepicker._adjustDate( event.target, ( event.ctrlKey ?
								-$.datepicker._get( inst, "stepBigMonths" ) :
								-$.datepicker._get( inst, "stepMonths" ) ), "M" );
						}

						// next month/year on alt +left on Mac
						break;
				case 38: if ( event.ctrlKey || event.metaKey ) {
							$.datepicker._adjustDate( event.target, -7, "D" );
						}
						handled = event.ctrlKey || event.metaKey;
						break; // -1 week on ctrl or command +up
				case 39: if ( event.ctrlKey || event.metaKey ) {
							$.datepicker._adjustDate( event.target, ( isRTL ? -1 : +1 ), "D" );
						}
						handled = event.ctrlKey || event.metaKey;

						// +1 day on ctrl or command +right
						if ( event.originalEvent.altKey ) {
							$.datepicker._adjustDate( event.target, ( event.ctrlKey ?
								+$.datepicker._get( inst, "stepBigMonths" ) :
								+$.datepicker._get( inst, "stepMonths" ) ), "M" );
						}

						// next month/year on alt +right
						break;
				case 40: if ( event.ctrlKey || event.metaKey ) {
							$.datepicker._adjustDate( event.target, +7, "D" );
						}
						handled = event.ctrlKey || event.metaKey;
						break; // +1 week on ctrl or command +down
				default: handled = false;
			}
		} else if ( event.keyCode === 36 && event.ctrlKey ) { // display the date picker on ctrl+home
			$.datepicker._showDatepicker( this );
		} else {
			handled = false;
		}

		if ( handled ) {
			event.preventDefault();
			event.stopPropagation();
		}
	},

	/* Filter entered characters - based on date format. */
	_doKeyPress: function( event ) {
		var chars, chr,
			inst = $.datepicker._getInst( event.target );

		if ( $.datepicker._get( inst, "constrainInput" ) ) {
			chars = $.datepicker._possibleChars( $.datepicker._get( inst, "dateFormat" ) );
			chr = String.fromCharCode( event.charCode == null ? event.keyCode : event.charCode );
			return event.ctrlKey || event.metaKey || ( chr < " " || !chars || chars.indexOf( chr ) > -1 );
		}
	},

	/* Synchronise manual entry and field/alternate field. */
	_doKeyUp: function( event ) {
		var date,
			inst = $.datepicker._getInst( event.target );

		if ( inst.input.val() !== inst.lastVal ) {
			try {
				date = $.datepicker.parseDate( $.datepicker._get( inst, "dateFormat" ),
					( inst.input ? inst.input.val() : null ),
					$.datepicker._getFormatConfig( inst ) );

				if ( date ) { // only if valid
					$.datepicker._setDateFromField( inst );
					$.datepicker._updateAlternate( inst );
					$.datepicker._updateDatepicker( inst );
				}
			}
			catch ( err ) {
			}
		}
		return true;
	},

	/* Pop-up the date picker for a given input field.
	 * If false returned from beforeShow event handler do not show.
	 * @param  input  element - the input field attached to the date picker or
	 *					event - if triggered by focus
	 */
	_showDatepicker: function( input ) {
		input = input.target || input;
		if ( input.nodeName.toLowerCase() !== "input" ) { // find from button/image trigger
			input = $( "input", input.parentNode )[ 0 ];
		}

		if ( $.datepicker._isDisabledDatepicker( input ) || $.datepicker._lastInput === input ) { // already here
			return;
		}

		var inst, beforeShow, beforeShowSettings, isFixed,
			offset, showAnim, duration;

		inst = $.datepicker._getInst( input );
		if ( $.datepicker._curInst && $.datepicker._curInst !== inst ) {
			$.datepicker._curInst.dpDiv.stop( true, true );
			if ( inst && $.datepicker._datepickerShowing ) {
				$.datepicker._hideDatepicker( $.datepicker._curInst.input[ 0 ] );
			}
		}

		beforeShow = $.datepicker._get( inst, "beforeShow" );
		beforeShowSettings = beforeShow ? beforeShow.apply( input, [ input, inst ] ) : {};
		if ( beforeShowSettings === false ) {
			return;
		}
		datepicker_extendRemove( inst.settings, beforeShowSettings );

		inst.lastVal = null;
		$.datepicker._lastInput = input;
		$.datepicker._setDateFromField( inst );

		if ( $.datepicker._inDialog ) { // hide cursor
			input.value = "";
		}
		if ( !$.datepicker._pos ) { // position below input
			$.datepicker._pos = $.datepicker._findPos( input );
			$.datepicker._pos[ 1 ] += input.offsetHeight; // add the height
		}

		isFixed = false;
		$( input ).parents().each( function() {
			isFixed |= $( this ).css( "position" ) === "fixed";
			return !isFixed;
		} );

		offset = { left: $.datepicker._pos[ 0 ], top: $.datepicker._pos[ 1 ] };
		$.datepicker._pos = null;

		//to avoid flashes on Firefox
		inst.dpDiv.empty();

		// determine sizing offscreen
		inst.dpDiv.css( { position: "absolute", display: "block", top: "-1000px" } );
		$.datepicker._updateDatepicker( inst );

		// fix width for dynamic number of date pickers
		// and adjust position before showing
		offset = $.datepicker._checkOffset( inst, offset, isFixed );
		inst.dpDiv.css( { position: ( $.datepicker._inDialog && $.blockUI ?
			"static" : ( isFixed ? "fixed" : "absolute" ) ), display: "none",
			left: offset.left + "px", top: offset.top + "px" } );

		if ( !inst.inline ) {
			showAnim = $.datepicker._get( inst, "showAnim" );
			duration = $.datepicker._get( inst, "duration" );
			inst.dpDiv.css( "z-index", datepicker_getZindex( $( input ) ) + 1 );
			$.datepicker._datepickerShowing = true;

			if ( $.effects && $.effects.effect[ showAnim ] ) {
				inst.dpDiv.show( showAnim, $.datepicker._get( inst, "showOptions" ), duration );
			} else {
				inst.dpDiv[ showAnim || "show" ]( showAnim ? duration : null );
			}

			if ( $.datepicker._shouldFocusInput( inst ) ) {
				inst.input.trigger( "focus" );
			}

			$.datepicker._curInst = inst;
		}
	},

	/* Generate the date picker content. */
	_updateDatepicker: function( inst ) {
		this.maxRows = 4; //Reset the max number of rows being displayed (see #7043)
		datepicker_instActive = inst; // for delegate hover events
		inst.dpDiv.empty().append( this._generateHTML( inst ) );
		this._attachHandlers( inst );

		var origyearshtml,
			numMonths = this._getNumberOfMonths( inst ),
			cols = numMonths[ 1 ],
			width = 17,
			activeCell = inst.dpDiv.find( "." + this._dayOverClass + " a" );

		if ( activeCell.length > 0 ) {
			datepicker_handleMouseover.apply( activeCell.get( 0 ) );
		}

		inst.dpDiv.removeClass( "ui-datepicker-multi-2 ui-datepicker-multi-3 ui-datepicker-multi-4" ).width( "" );
		if ( cols > 1 ) {
			inst.dpDiv.addClass( "ui-datepicker-multi-" + cols ).css( "width", ( width * cols ) + "em" );
		}
		inst.dpDiv[ ( numMonths[ 0 ] !== 1 || numMonths[ 1 ] !== 1 ? "add" : "remove" ) +
			"Class" ]( "ui-datepicker-multi" );
		inst.dpDiv[ ( this._get( inst, "isRTL" ) ? "add" : "remove" ) +
			"Class" ]( "ui-datepicker-rtl" );

		if ( inst === $.datepicker._curInst && $.datepicker._datepickerShowing && $.datepicker._shouldFocusInput( inst ) ) {
			inst.input.trigger( "focus" );
		}

		// Deffered render of the years select (to avoid flashes on Firefox)
		if ( inst.yearshtml ) {
			origyearshtml = inst.yearshtml;
			setTimeout( function() {

				//assure that inst.yearshtml didn't change.
				if ( origyearshtml === inst.yearshtml && inst.yearshtml ) {
					inst.dpDiv.find( "select.ui-datepicker-year:first" ).replaceWith( inst.yearshtml );
				}
				origyearshtml = inst.yearshtml = null;
			}, 0 );
		}
	},

	// #6694 - don't focus the input if it's already focused
	// this breaks the change event in IE
	// Support: IE and jQuery <1.9
	_shouldFocusInput: function( inst ) {
		return inst.input && inst.input.is( ":visible" ) && !inst.input.is( ":disabled" ) && !inst.input.is( ":focus" );
	},

	/* Check positioning to remain on screen. */
	_checkOffset: function( inst, offset, isFixed ) {
		var dpWidth = inst.dpDiv.outerWidth(),
			dpHeight = inst.dpDiv.outerHeight(),
			inputWidth = inst.input ? inst.input.outerWidth() : 0,
			inputHeight = inst.input ? inst.input.outerHeight() : 0,
			viewWidth = document.documentElement.clientWidth + ( isFixed ? 0 : $( document ).scrollLeft() ),
			viewHeight = document.documentElement.clientHeight + ( isFixed ? 0 : $( document ).scrollTop() );

		offset.left -= ( this._get( inst, "isRTL" ) ? ( dpWidth - inputWidth ) : 0 );
		offset.left -= ( isFixed && offset.left === inst.input.offset().left ) ? $( document ).scrollLeft() : 0;
		offset.top -= ( isFixed && offset.top === ( inst.input.offset().top + inputHeight ) ) ? $( document ).scrollTop() : 0;

		// Now check if datepicker is showing outside window viewport - move to a better place if so.
		offset.left -= Math.min( offset.left, ( offset.left + dpWidth > viewWidth && viewWidth > dpWidth ) ?
			Math.abs( offset.left + dpWidth - viewWidth ) : 0 );
		offset.top -= Math.min( offset.top, ( offset.top + dpHeight > viewHeight && viewHeight > dpHeight ) ?
			Math.abs( dpHeight + inputHeight ) : 0 );

		return offset;
	},

	/* Find an object's position on the screen. */
	_findPos: function( obj ) {
		var position,
			inst = this._getInst( obj ),
			isRTL = this._get( inst, "isRTL" );

		while ( obj && ( obj.type === "hidden" || obj.nodeType !== 1 || $.expr.filters.hidden( obj ) ) ) {
			obj = obj[ isRTL ? "previousSibling" : "nextSibling" ];
		}

		position = $( obj ).offset();
		return [ position.left, position.top ];
	},

	/* Hide the date picker from view.
	 * @param  input  element - the input field attached to the date picker
	 */
	_hideDatepicker: function( input ) {
		var showAnim, duration, postProcess, onClose,
			inst = this._curInst;

		if ( !inst || ( input && inst !== $.data( input, "datepicker" ) ) ) {
			return;
		}

		if ( this._datepickerShowing ) {
			showAnim = this._get( inst, "showAnim" );
			duration = this._get( inst, "duration" );
			postProcess = function() {
				$.datepicker._tidyDialog( inst );
			};

			// DEPRECATED: after BC for 1.8.x $.effects[ showAnim ] is not needed
			if ( $.effects && ( $.effects.effect[ showAnim ] || $.effects[ showAnim ] ) ) {
				inst.dpDiv.hide( showAnim, $.datepicker._get( inst, "showOptions" ), duration, postProcess );
			} else {
				inst.dpDiv[ ( showAnim === "slideDown" ? "slideUp" :
					( showAnim === "fadeIn" ? "fadeOut" : "hide" ) ) ]( ( showAnim ? duration : null ), postProcess );
			}

			if ( !showAnim ) {
				postProcess();
			}
			this._datepickerShowing = false;

			onClose = this._get( inst, "onClose" );
			if ( onClose ) {
				onClose.apply( ( inst.input ? inst.input[ 0 ] : null ), [ ( inst.input ? inst.input.val() : "" ), inst ] );
			}

			this._lastInput = null;
			if ( this._inDialog ) {
				this._dialogInput.css( { position: "absolute", left: "0", top: "-100px" } );
				if ( $.blockUI ) {
					$.unblockUI();
					$( "body" ).append( this.dpDiv );
				}
			}
			this._inDialog = false;
		}
	},

	/* Tidy up after a dialog display. */
	_tidyDialog: function( inst ) {
		inst.dpDiv.removeClass( this._dialogClass ).off( ".ui-datepicker-calendar" );
	},

	/* Close date picker if clicked elsewhere. */
	_checkExternalClick: function( event ) {
		if ( !$.datepicker._curInst ) {
			return;
		}

		var $target = $( event.target ),
			inst = $.datepicker._getInst( $target[ 0 ] );

		if ( ( ( $target[ 0 ].id !== $.datepicker._mainDivId &&
				$target.parents( "#" + $.datepicker._mainDivId ).length === 0 &&
				!$target.hasClass( $.datepicker.markerClassName ) &&
				!$target.closest( "." + $.datepicker._triggerClass ).length &&
				$.datepicker._datepickerShowing && !( $.datepicker._inDialog && $.blockUI ) ) ) ||
			( $target.hasClass( $.datepicker.markerClassName ) && $.datepicker._curInst !== inst ) ) {
				$.datepicker._hideDatepicker();
		}
	},

	/* Adjust one of the date sub-fields. */
	_adjustDate: function( id, offset, period ) {
		var target = $( id ),
			inst = this._getInst( target[ 0 ] );

		if ( this._isDisabledDatepicker( target[ 0 ] ) ) {
			return;
		}
		this._adjustInstDate( inst, offset +
			( period === "M" ? this._get( inst, "showCurrentAtPos" ) : 0 ), // undo positioning
			period );
		this._updateDatepicker( inst );
	},

	/* Action for current link. */
	_gotoToday: function( id ) {
		var date,
			target = $( id ),
			inst = this._getInst( target[ 0 ] );

		if ( this._get( inst, "gotoCurrent" ) && inst.currentDay ) {
			inst.selectedDay = inst.currentDay;
			inst.drawMonth = inst.selectedMonth = inst.currentMonth;
			inst.drawYear = inst.selectedYear = inst.currentYear;
		} else {
			date = new Date();
			inst.selectedDay = date.getDate();
			inst.drawMonth = inst.selectedMonth = date.getMonth();
			inst.drawYear = inst.selectedYear = date.getFullYear();
		}
		this._notifyChange( inst );
		this._adjustDate( target );
	},

	/* Action for selecting a new month/year. */
	_selectMonthYear: function( id, select, period ) {
		var target = $( id ),
			inst = this._getInst( target[ 0 ] );

		inst[ "selected" + ( period === "M" ? "Month" : "Year" ) ] =
		inst[ "draw" + ( period === "M" ? "Month" : "Year" ) ] =
			parseInt( select.options[ select.selectedIndex ].value, 10 );

		this._notifyChange( inst );
		this._adjustDate( target );
	},

	/* Action for selecting a day. */
	_selectDay: function( id, month, year, td ) {
		var inst,
			target = $( id );

		if ( $( td ).hasClass( this._unselectableClass ) || this._isDisabledDatepicker( target[ 0 ] ) ) {
			return;
		}

		inst = this._getInst( target[ 0 ] );
		inst.selectedDay = inst.currentDay = $( "a", td ).html();
		inst.selectedMonth = inst.currentMonth = month;
		inst.selectedYear = inst.currentYear = year;
		this._selectDate( id, this._formatDate( inst,
			inst.currentDay, inst.currentMonth, inst.currentYear ) );
	},

	/* Erase the input field and hide the date picker. */
	_clearDate: function( id ) {
		var target = $( id );
		this._selectDate( target, "" );
	},

	/* Update the input field with the selected date. */
	_selectDate: function( id, dateStr ) {
		var onSelect,
			target = $( id ),
			inst = this._getInst( target[ 0 ] );

		dateStr = ( dateStr != null ? dateStr : this._formatDate( inst ) );
		if ( inst.input ) {
			inst.input.val( dateStr );
		}
		this._updateAlternate( inst );

		onSelect = this._get( inst, "onSelect" );
		if ( onSelect ) {
			onSelect.apply( ( inst.input ? inst.input[ 0 ] : null ), [ dateStr, inst ] );  // trigger custom callback
		} else if ( inst.input ) {
			inst.input.trigger( "change" ); // fire the change event
		}

		if ( inst.inline ) {
			this._updateDatepicker( inst );
		} else {
			this._hideDatepicker();
			this._lastInput = inst.input[ 0 ];
			if ( typeof( inst.input[ 0 ] ) !== "object" ) {
				inst.input.trigger( "focus" ); // restore focus
			}
			this._lastInput = null;
		}
	},

	/* Update any alternate field to synchronise with the main field. */
	_updateAlternate: function( inst ) {
		var altFormat, date, dateStr,
			altField = this._get( inst, "altField" );

		if ( altField ) { // update alternate field too
			altFormat = this._get( inst, "altFormat" ) || this._get( inst, "dateFormat" );
			date = this._getDate( inst );
			dateStr = this.formatDate( altFormat, date, this._getFormatConfig( inst ) );
			$( altField ).val( dateStr );
		}
	},

	/* Set as beforeShowDay function to prevent selection of weekends.
	 * @param  date  Date - the date to customise
	 * @return [boolean, string] - is this date selectable?, what is its CSS class?
	 */
	noWeekends: function( date ) {
		var day = date.getDay();
		return [ ( day > 0 && day < 6 ), "" ];
	},

	/* Set as calculateWeek to determine the week of the year based on the ISO 8601 definition.
	 * @param  date  Date - the date to get the week for
	 * @return  number - the number of the week within the year that contains this date
	 */
	iso8601Week: function( date ) {
		var time,
			checkDate = new Date( date.getTime() );

		// Find Thursday of this week starting on Monday
		checkDate.setDate( checkDate.getDate() + 4 - ( checkDate.getDay() || 7 ) );

		time = checkDate.getTime();
		checkDate.setMonth( 0 ); // Compare with Jan 1
		checkDate.setDate( 1 );
		return Math.floor( Math.round( ( time - checkDate ) / 86400000 ) / 7 ) + 1;
	},

	/* Parse a string value into a date object.
	 * See formatDate below for the possible formats.
	 *
	 * @param  format string - the expected format of the date
	 * @param  value string - the date in the above format
	 * @param  settings Object - attributes include:
	 *					shortYearCutoff  number - the cutoff year for determining the century (optional)
	 *					dayNamesShort	string[7] - abbreviated names of the days from Sunday (optional)
	 *					dayNames		string[7] - names of the days from Sunday (optional)
	 *					monthNamesShort string[12] - abbreviated names of the months (optional)
	 *					monthNames		string[12] - names of the months (optional)
	 * @return  Date - the extracted date value or null if value is blank
	 */
	parseDate: function( format, value, settings ) {
		if ( format == null || value == null ) {
			throw "Invalid arguments";
		}

		value = ( typeof value === "object" ? value.toString() : value + "" );
		if ( value === "" ) {
			return null;
		}

		var iFormat, dim, extra,
			iValue = 0,
			shortYearCutoffTemp = ( settings ? settings.shortYearCutoff : null ) || this._defaults.shortYearCutoff,
			shortYearCutoff = ( typeof shortYearCutoffTemp !== "string" ? shortYearCutoffTemp :
				new Date().getFullYear() % 100 + parseInt( shortYearCutoffTemp, 10 ) ),
			dayNamesShort = ( settings ? settings.dayNamesShort : null ) || this._defaults.dayNamesShort,
			dayNames = ( settings ? settings.dayNames : null ) || this._defaults.dayNames,
			monthNamesShort = ( settings ? settings.monthNamesShort : null ) || this._defaults.monthNamesShort,
			monthNames = ( settings ? settings.monthNames : null ) || this._defaults.monthNames,
			year = -1,
			month = -1,
			day = -1,
			doy = -1,
			literal = false,
			date,

			// Check whether a format character is doubled
			lookAhead = function( match ) {
				var matches = ( iFormat + 1 < format.length && format.charAt( iFormat + 1 ) === match );
				if ( matches ) {
					iFormat++;
				}
				return matches;
			},

			// Extract a number from the string value
			getNumber = function( match ) {
				var isDoubled = lookAhead( match ),
					size = ( match === "@" ? 14 : ( match === "!" ? 20 :
					( match === "y" && isDoubled ? 4 : ( match === "o" ? 3 : 2 ) ) ) ),
					minSize = ( match === "y" ? size : 1 ),
					digits = new RegExp( "^\\d{" + minSize + "," + size + "}" ),
					num = value.substring( iValue ).match( digits );
				if ( !num ) {
					throw "Missing number at position " + iValue;
				}
				iValue += num[ 0 ].length;
				return parseInt( num[ 0 ], 10 );
			},

			// Extract a name from the string value and convert to an index
			getName = function( match, shortNames, longNames ) {
				var index = -1,
					names = $.map( lookAhead( match ) ? longNames : shortNames, function( v, k ) {
						return [ [ k, v ] ];
					} ).sort( function( a, b ) {
						return -( a[ 1 ].length - b[ 1 ].length );
					} );

				$.each( names, function( i, pair ) {
					var name = pair[ 1 ];
					if ( value.substr( iValue, name.length ).toLowerCase() === name.toLowerCase() ) {
						index = pair[ 0 ];
						iValue += name.length;
						return false;
					}
				} );
				if ( index !== -1 ) {
					return index + 1;
				} else {
					throw "Unknown name at position " + iValue;
				}
			},

			// Confirm that a literal character matches the string value
			checkLiteral = function() {
				if ( value.charAt( iValue ) !== format.charAt( iFormat ) ) {
					throw "Unexpected literal at position " + iValue;
				}
				iValue++;
			};

		for ( iFormat = 0; iFormat < format.length; iFormat++ ) {
			if ( literal ) {
				if ( format.charAt( iFormat ) === "'" && !lookAhead( "'" ) ) {
					literal = false;
				} else {
					checkLiteral();
				}
			} else {
				switch ( format.charAt( iFormat ) ) {
					case "d":
						day = getNumber( "d" );
						break;
					case "D":
						getName( "D", dayNamesShort, dayNames );
						break;
					case "o":
						doy = getNumber( "o" );
						break;
					case "m":
						month = getNumber( "m" );
						break;
					case "M":
						month = getName( "M", monthNamesShort, monthNames );
						break;
					case "y":
						year = getNumber( "y" );
						break;
					case "@":
						date = new Date( getNumber( "@" ) );
						year = date.getFullYear();
						month = date.getMonth() + 1;
						day = date.getDate();
						break;
					case "!":
						date = new Date( ( getNumber( "!" ) - this._ticksTo1970 ) / 10000 );
						year = date.getFullYear();
						month = date.getMonth() + 1;
						day = date.getDate();
						break;
					case "'":
						if ( lookAhead( "'" ) ) {
							checkLiteral();
						} else {
							literal = true;
						}
						break;
					default:
						checkLiteral();
				}
			}
		}

		if ( iValue < value.length ) {
			extra = value.substr( iValue );
			if ( !/^\s+/.test( extra ) ) {
				throw "Extra/unparsed characters found in date: " + extra;
			}
		}

		if ( year === -1 ) {
			year = new Date().getFullYear();
		} else if ( year < 100 ) {
			year += new Date().getFullYear() - new Date().getFullYear() % 100 +
				( year <= shortYearCutoff ? 0 : -100 );
		}

		if ( doy > -1 ) {
			month = 1;
			day = doy;
			do {
				dim = this._getDaysInMonth( year, month - 1 );
				if ( day <= dim ) {
					break;
				}
				month++;
				day -= dim;
			} while ( true );
		}

		date = this._daylightSavingAdjust( new Date( year, month - 1, day ) );
		if ( date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day ) {
			throw "Invalid date"; // E.g. 31/02/00
		}
		return date;
	},

	/* Standard date formats. */
	ATOM: "yy-mm-dd", // RFC 3339 (ISO 8601)
	COOKIE: "D, dd M yy",
	ISO_8601: "yy-mm-dd",
	RFC_822: "D, d M y",
	RFC_850: "DD, dd-M-y",
	RFC_1036: "D, d M y",
	RFC_1123: "D, d M yy",
	RFC_2822: "D, d M yy",
	RSS: "D, d M y", // RFC 822
	TICKS: "!",
	TIMESTAMP: "@",
	W3C: "yy-mm-dd", // ISO 8601

	_ticksTo1970: ( ( ( 1970 - 1 ) * 365 + Math.floor( 1970 / 4 ) - Math.floor( 1970 / 100 ) +
		Math.floor( 1970 / 400 ) ) * 24 * 60 * 60 * 10000000 ),

	/* Format a date object into a string value.
	 * The format can be combinations of the following:
	 * d  - day of month (no leading zero)
	 * dd - day of month (two digit)
	 * o  - day of year (no leading zeros)
	 * oo - day of year (three digit)
	 * D  - day name short
	 * DD - day name long
	 * m  - month of year (no leading zero)
	 * mm - month of year (two digit)
	 * M  - month name short
	 * MM - month name long
	 * y  - year (two digit)
	 * yy - year (four digit)
	 * @ - Unix timestamp (ms since 01/01/1970)
	 * ! - Windows ticks (100ns since 01/01/0001)
	 * "..." - literal text
	 * '' - single quote
	 *
	 * @param  format string - the desired format of the date
	 * @param  date Date - the date value to format
	 * @param  settings Object - attributes include:
	 *					dayNamesShort	string[7] - abbreviated names of the days from Sunday (optional)
	 *					dayNames		string[7] - names of the days from Sunday (optional)
	 *					monthNamesShort string[12] - abbreviated names of the months (optional)
	 *					monthNames		string[12] - names of the months (optional)
	 * @return  string - the date in the above format
	 */
	formatDate: function( format, date, settings ) {
		if ( !date ) {
			return "";
		}

		var iFormat,
			dayNamesShort = ( settings ? settings.dayNamesShort : null ) || this._defaults.dayNamesShort,
			dayNames = ( settings ? settings.dayNames : null ) || this._defaults.dayNames,
			monthNamesShort = ( settings ? settings.monthNamesShort : null ) || this._defaults.monthNamesShort,
			monthNames = ( settings ? settings.monthNames : null ) || this._defaults.monthNames,

			// Check whether a format character is doubled
			lookAhead = function( match ) {
				var matches = ( iFormat + 1 < format.length && format.charAt( iFormat + 1 ) === match );
				if ( matches ) {
					iFormat++;
				}
				return matches;
			},

			// Format a number, with leading zero if necessary
			formatNumber = function( match, value, len ) {
				var num = "" + value;
				if ( lookAhead( match ) ) {
					while ( num.length < len ) {
						num = "0" + num;
					}
				}
				return num;
			},

			// Format a name, short or long as requested
			formatName = function( match, value, shortNames, longNames ) {
				return ( lookAhead( match ) ? longNames[ value ] : shortNames[ value ] );
			},
			output = "",
			literal = false;

		if ( date ) {
			for ( iFormat = 0; iFormat < format.length; iFormat++ ) {
				if ( literal ) {
					if ( format.charAt( iFormat ) === "'" && !lookAhead( "'" ) ) {
						literal = false;
					} else {
						output += format.charAt( iFormat );
					}
				} else {
					switch ( format.charAt( iFormat ) ) {
						case "d":
							output += formatNumber( "d", date.getDate(), 2 );
							break;
						case "D":
							output += formatName( "D", date.getDay(), dayNamesShort, dayNames );
							break;
						case "o":
							output += formatNumber( "o",
								Math.round( ( new Date( date.getFullYear(), date.getMonth(), date.getDate() ).getTime() - new Date( date.getFullYear(), 0, 0 ).getTime() ) / 86400000 ), 3 );
							break;
						case "m":
							output += formatNumber( "m", date.getMonth() + 1, 2 );
							break;
						case "M":
							output += formatName( "M", date.getMonth(), monthNamesShort, monthNames );
							break;
						case "y":
							output += ( lookAhead( "y" ) ? date.getFullYear() :
								( date.getFullYear() % 100 < 10 ? "0" : "" ) + date.getFullYear() % 100 );
							break;
						case "@":
							output += date.getTime();
							break;
						case "!":
							output += date.getTime() * 10000 + this._ticksTo1970;
							break;
						case "'":
							if ( lookAhead( "'" ) ) {
								output += "'";
							} else {
								literal = true;
							}
							break;
						default:
							output += format.charAt( iFormat );
					}
				}
			}
		}
		return output;
	},

	/* Extract all possible characters from the date format. */
	_possibleChars: function( format ) {
		var iFormat,
			chars = "",
			literal = false,

			// Check whether a format character is doubled
			lookAhead = function( match ) {
				var matches = ( iFormat + 1 < format.length && format.charAt( iFormat + 1 ) === match );
				if ( matches ) {
					iFormat++;
				}
				return matches;
			};

		for ( iFormat = 0; iFormat < format.length; iFormat++ ) {
			if ( literal ) {
				if ( format.charAt( iFormat ) === "'" && !lookAhead( "'" ) ) {
					literal = false;
				} else {
					chars += format.charAt( iFormat );
				}
			} else {
				switch ( format.charAt( iFormat ) ) {
					case "d": case "m": case "y": case "@":
						chars += "0123456789";
						break;
					case "D": case "M":
						return null; // Accept anything
					case "'":
						if ( lookAhead( "'" ) ) {
							chars += "'";
						} else {
							literal = true;
						}
						break;
					default:
						chars += format.charAt( iFormat );
				}
			}
		}
		return chars;
	},

	/* Get a setting value, defaulting if necessary. */
	_get: function( inst, name ) {
		return inst.settings[ name ] !== undefined ?
			inst.settings[ name ] : this._defaults[ name ];
	},

	/* Parse existing date and initialise date picker. */
	_setDateFromField: function( inst, noDefault ) {
		if ( inst.input.val() === inst.lastVal ) {
			return;
		}

		var dateFormat = this._get( inst, "dateFormat" ),
			dates = inst.lastVal = inst.input ? inst.input.val() : null,
			defaultDate = this._getDefaultDate( inst ),
			date = defaultDate,
			settings = this._getFormatConfig( inst );

		try {
			date = this.parseDate( dateFormat, dates, settings ) || defaultDate;
		} catch ( event ) {
			dates = ( noDefault ? "" : dates );
		}
		inst.selectedDay = date.getDate();
		inst.drawMonth = inst.selectedMonth = date.getMonth();
		inst.drawYear = inst.selectedYear = date.getFullYear();
		inst.currentDay = ( dates ? date.getDate() : 0 );
		inst.currentMonth = ( dates ? date.getMonth() : 0 );
		inst.currentYear = ( dates ? date.getFullYear() : 0 );
		this._adjustInstDate( inst );
	},

	/* Retrieve the default date shown on opening. */
	_getDefaultDate: function( inst ) {
		return this._restrictMinMax( inst,
			this._determineDate( inst, this._get( inst, "defaultDate" ), new Date() ) );
	},

	/* A date may be specified as an exact value or a relative one. */
	_determineDate: function( inst, date, defaultDate ) {
		var offsetNumeric = function( offset ) {
				var date = new Date();
				date.setDate( date.getDate() + offset );
				return date;
			},
			offsetString = function( offset ) {
				try {
					return $.datepicker.parseDate( $.datepicker._get( inst, "dateFormat" ),
						offset, $.datepicker._getFormatConfig( inst ) );
				}
				catch ( e ) {

					// Ignore
				}

				var date = ( offset.toLowerCase().match( /^c/ ) ?
					$.datepicker._getDate( inst ) : null ) || new Date(),
					year = date.getFullYear(),
					month = date.getMonth(),
					day = date.getDate(),
					pattern = /([+\-]?[0-9]+)\s*(d|D|w|W|m|M|y|Y)?/g,
					matches = pattern.exec( offset );

				while ( matches ) {
					switch ( matches[ 2 ] || "d" ) {
						case "d" : case "D" :
							day += parseInt( matches[ 1 ], 10 ); break;
						case "w" : case "W" :
							day += parseInt( matches[ 1 ], 10 ) * 7; break;
						case "m" : case "M" :
							month += parseInt( matches[ 1 ], 10 );
							day = Math.min( day, $.datepicker._getDaysInMonth( year, month ) );
							break;
						case "y": case "Y" :
							year += parseInt( matches[ 1 ], 10 );
							day = Math.min( day, $.datepicker._getDaysInMonth( year, month ) );
							break;
					}
					matches = pattern.exec( offset );
				}
				return new Date( year, month, day );
			},
			newDate = ( date == null || date === "" ? defaultDate : ( typeof date === "string" ? offsetString( date ) :
				( typeof date === "number" ? ( isNaN( date ) ? defaultDate : offsetNumeric( date ) ) : new Date( date.getTime() ) ) ) );

		newDate = ( newDate && newDate.toString() === "Invalid Date" ? defaultDate : newDate );
		if ( newDate ) {
			newDate.setHours( 0 );
			newDate.setMinutes( 0 );
			newDate.setSeconds( 0 );
			newDate.setMilliseconds( 0 );
		}
		return this._daylightSavingAdjust( newDate );
	},

	/* Handle switch to/from daylight saving.
	 * Hours may be non-zero on daylight saving cut-over:
	 * > 12 when midnight changeover, but then cannot generate
	 * midnight datetime, so jump to 1AM, otherwise reset.
	 * @param  date  (Date) the date to check
	 * @return  (Date) the corrected date
	 */
	_daylightSavingAdjust: function( date ) {
		if ( !date ) {
			return null;
		}
		date.setHours( date.getHours() > 12 ? date.getHours() + 2 : 0 );
		return date;
	},

	/* Set the date(s) directly. */
	_setDate: function( inst, date, noChange ) {
		var clear = !date,
			origMonth = inst.selectedMonth,
			origYear = inst.selectedYear,
			newDate = this._restrictMinMax( inst, this._determineDate( inst, date, new Date() ) );

		inst.selectedDay = inst.currentDay = newDate.getDate();
		inst.drawMonth = inst.selectedMonth = inst.currentMonth = newDate.getMonth();
		inst.drawYear = inst.selectedYear = inst.currentYear = newDate.getFullYear();
		if ( ( origMonth !== inst.selectedMonth || origYear !== inst.selectedYear ) && !noChange ) {
			this._notifyChange( inst );
		}
		this._adjustInstDate( inst );
		if ( inst.input ) {
			inst.input.val( clear ? "" : this._formatDate( inst ) );
		}
	},

	/* Retrieve the date(s) directly. */
	_getDate: function( inst ) {
		var startDate = ( !inst.currentYear || ( inst.input && inst.input.val() === "" ) ? null :
			this._daylightSavingAdjust( new Date(
			inst.currentYear, inst.currentMonth, inst.currentDay ) ) );
			return startDate;
	},

	/* Attach the onxxx handlers.  These are declared statically so
	 * they work with static code transformers like Caja.
	 */
	_attachHandlers: function( inst ) {
		var stepMonths = this._get( inst, "stepMonths" ),
			id = "#" + inst.id.replace( /\\\\/g, "\\" );
		inst.dpDiv.find( "[data-handler]" ).map( function() {
			var handler = {
				prev: function() {
					$.datepicker._adjustDate( id, -stepMonths, "M" );
				},
				next: function() {
					$.datepicker._adjustDate( id, +stepMonths, "M" );
				},
				hide: function() {
					$.datepicker._hideDatepicker();
				},
				today: function() {
					$.datepicker._gotoToday( id );
				},
				selectDay: function() {
					$.datepicker._selectDay( id, +this.getAttribute( "data-month" ), +this.getAttribute( "data-year" ), this );
					return false;
				},
				selectMonth: function() {
					$.datepicker._selectMonthYear( id, this, "M" );
					return false;
				},
				selectYear: function() {
					$.datepicker._selectMonthYear( id, this, "Y" );
					return false;
				}
			};
			$( this ).on( this.getAttribute( "data-event" ), handler[ this.getAttribute( "data-handler" ) ] );
		} );
	},

	/* Generate the HTML for the current state of the date picker. */
	_generateHTML: function( inst ) {
		var maxDraw, prevText, prev, nextText, next, currentText, gotoDate,
			controls, buttonPanel, firstDay, showWeek, dayNames, dayNamesMin,
			monthNames, monthNamesShort, beforeShowDay, showOtherMonths,
			selectOtherMonths, defaultDate, html, dow, row, group, col, selectedDate,
			cornerClass, calender, thead, day, daysInMonth, leadDays, curRows, numRows,
			printDate, dRow, tbody, daySettings, otherMonth, unselectable,
			tempDate = new Date(),
			today = this._daylightSavingAdjust(
				new Date( tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate() ) ), // clear time
			isRTL = this._get( inst, "isRTL" ),
			showButtonPanel = this._get( inst, "showButtonPanel" ),
			hideIfNoPrevNext = this._get( inst, "hideIfNoPrevNext" ),
			navigationAsDateFormat = this._get( inst, "navigationAsDateFormat" ),
			numMonths = this._getNumberOfMonths( inst ),
			showCurrentAtPos = this._get( inst, "showCurrentAtPos" ),
			stepMonths = this._get( inst, "stepMonths" ),
			isMultiMonth = ( numMonths[ 0 ] !== 1 || numMonths[ 1 ] !== 1 ),
			currentDate = this._daylightSavingAdjust( ( !inst.currentDay ? new Date( 9999, 9, 9 ) :
				new Date( inst.currentYear, inst.currentMonth, inst.currentDay ) ) ),
			minDate = this._getMinMaxDate( inst, "min" ),
			maxDate = this._getMinMaxDate( inst, "max" ),
			drawMonth = inst.drawMonth - showCurrentAtPos,
			drawYear = inst.drawYear;

		if ( drawMonth < 0 ) {
			drawMonth += 12;
			drawYear--;
		}
		if ( maxDate ) {
			maxDraw = this._daylightSavingAdjust( new Date( maxDate.getFullYear(),
				maxDate.getMonth() - ( numMonths[ 0 ] * numMonths[ 1 ] ) + 1, maxDate.getDate() ) );
			maxDraw = ( minDate && maxDraw < minDate ? minDate : maxDraw );
			while ( this._daylightSavingAdjust( new Date( drawYear, drawMonth, 1 ) ) > maxDraw ) {
				drawMonth--;
				if ( drawMonth < 0 ) {
					drawMonth = 11;
					drawYear--;
				}
			}
		}
		inst.drawMonth = drawMonth;
		inst.drawYear = drawYear;

		prevText = this._get( inst, "prevText" );
		prevText = ( !navigationAsDateFormat ? prevText : this.formatDate( prevText,
			this._daylightSavingAdjust( new Date( drawYear, drawMonth - stepMonths, 1 ) ),
			this._getFormatConfig( inst ) ) );

		prev = ( this._canAdjustMonth( inst, -1, drawYear, drawMonth ) ?
			"<a class='ui-datepicker-prev ui-corner-all' data-handler='prev' data-event='click'" +
			" title='" + prevText + "'><span class='ui-icon ui-icon-circle-triangle-" + ( isRTL ? "e" : "w" ) + "'>" + prevText + "</span></a>" :
			( hideIfNoPrevNext ? "" : "<a class='ui-datepicker-prev ui-corner-all ui-state-disabled' title='" + prevText + "'><span class='ui-icon ui-icon-circle-triangle-" + ( isRTL ? "e" : "w" ) + "'>" + prevText + "</span></a>" ) );

		nextText = this._get( inst, "nextText" );
		nextText = ( !navigationAsDateFormat ? nextText : this.formatDate( nextText,
			this._daylightSavingAdjust( new Date( drawYear, drawMonth + stepMonths, 1 ) ),
			this._getFormatConfig( inst ) ) );

		next = ( this._canAdjustMonth( inst, +1, drawYear, drawMonth ) ?
			"<a class='ui-datepicker-next ui-corner-all' data-handler='next' data-event='click'" +
			" title='" + nextText + "'><span class='ui-icon ui-icon-circle-triangle-" + ( isRTL ? "w" : "e" ) + "'>" + nextText + "</span></a>" :
			( hideIfNoPrevNext ? "" : "<a class='ui-datepicker-next ui-corner-all ui-state-disabled' title='" + nextText + "'><span class='ui-icon ui-icon-circle-triangle-" + ( isRTL ? "w" : "e" ) + "'>" + nextText + "</span></a>" ) );

		currentText = this._get( inst, "currentText" );
		gotoDate = ( this._get( inst, "gotoCurrent" ) && inst.currentDay ? currentDate : today );
		currentText = ( !navigationAsDateFormat ? currentText :
			this.formatDate( currentText, gotoDate, this._getFormatConfig( inst ) ) );

		controls = ( !inst.inline ? "<button type='button' class='ui-datepicker-close ui-state-default ui-priority-primary ui-corner-all' data-handler='hide' data-event='click'>" +
			this._get( inst, "closeText" ) + "</button>" : "" );

		buttonPanel = ( showButtonPanel ) ? "<div class='ui-datepicker-buttonpane ui-widget-content'>" + ( isRTL ? controls : "" ) +
			( this._isInRange( inst, gotoDate ) ? "<button type='button' class='ui-datepicker-current ui-state-default ui-priority-secondary ui-corner-all' data-handler='today' data-event='click'" +
			">" + currentText + "</button>" : "" ) + ( isRTL ? "" : controls ) + "</div>" : "";

		firstDay = parseInt( this._get( inst, "firstDay" ), 10 );
		firstDay = ( isNaN( firstDay ) ? 0 : firstDay );

		showWeek = this._get( inst, "showWeek" );
		dayNames = this._get( inst, "dayNames" );
		dayNamesMin = this._get( inst, "dayNamesMin" );
		monthNames = this._get( inst, "monthNames" );
		monthNamesShort = this._get( inst, "monthNamesShort" );
		beforeShowDay = this._get( inst, "beforeShowDay" );
		showOtherMonths = this._get( inst, "showOtherMonths" );
		selectOtherMonths = this._get( inst, "selectOtherMonths" );
		defaultDate = this._getDefaultDate( inst );
		html = "";

		for ( row = 0; row < numMonths[ 0 ]; row++ ) {
			group = "";
			this.maxRows = 4;
			for ( col = 0; col < numMonths[ 1 ]; col++ ) {
				selectedDate = this._daylightSavingAdjust( new Date( drawYear, drawMonth, inst.selectedDay ) );
				cornerClass = " ui-corner-all";
				calender = "";
				if ( isMultiMonth ) {
					calender += "<div class='ui-datepicker-group";
					if ( numMonths[ 1 ] > 1 ) {
						switch ( col ) {
							case 0: calender += " ui-datepicker-group-first";
								cornerClass = " ui-corner-" + ( isRTL ? "right" : "left" ); break;
							case numMonths[ 1 ] - 1: calender += " ui-datepicker-group-last";
								cornerClass = " ui-corner-" + ( isRTL ? "left" : "right" ); break;
							default: calender += " ui-datepicker-group-middle"; cornerClass = ""; break;
						}
					}
					calender += "'>";
				}
				calender += "<div class='ui-datepicker-header ui-widget-header ui-helper-clearfix" + cornerClass + "'>" +
					( /all|left/.test( cornerClass ) && row === 0 ? ( isRTL ? next : prev ) : "" ) +
					( /all|right/.test( cornerClass ) && row === 0 ? ( isRTL ? prev : next ) : "" ) +
					this._generateMonthYearHeader( inst, drawMonth, drawYear, minDate, maxDate,
					row > 0 || col > 0, monthNames, monthNamesShort ) + // draw month headers
					"</div><table class='ui-datepicker-calendar'><thead>" +
					"<tr>";
				thead = ( showWeek ? "<th class='ui-datepicker-week-col'>" + this._get( inst, "weekHeader" ) + "</th>" : "" );
				for ( dow = 0; dow < 7; dow++ ) { // days of the week
					day = ( dow + firstDay ) % 7;
					thead += "<th scope='col'" + ( ( dow + firstDay + 6 ) % 7 >= 5 ? " class='ui-datepicker-week-end'" : "" ) + ">" +
						"<span title='" + dayNames[ day ] + "'>" + dayNamesMin[ day ] + "</span></th>";
				}
				calender += thead + "</tr></thead><tbody>";
				daysInMonth = this._getDaysInMonth( drawYear, drawMonth );
				if ( drawYear === inst.selectedYear && drawMonth === inst.selectedMonth ) {
					inst.selectedDay = Math.min( inst.selectedDay, daysInMonth );
				}
				leadDays = ( this._getFirstDayOfMonth( drawYear, drawMonth ) - firstDay + 7 ) % 7;
				curRows = Math.ceil( ( leadDays + daysInMonth ) / 7 ); // calculate the number of rows to generate
				numRows = ( isMultiMonth ? this.maxRows > curRows ? this.maxRows : curRows : curRows ); //If multiple months, use the higher number of rows (see #7043)
				this.maxRows = numRows;
				printDate = this._daylightSavingAdjust( new Date( drawYear, drawMonth, 1 - leadDays ) );
				for ( dRow = 0; dRow < numRows; dRow++ ) { // create date picker rows
					calender += "<tr>";
					tbody = ( !showWeek ? "" : "<td class='ui-datepicker-week-col'>" +
						this._get( inst, "calculateWeek" )( printDate ) + "</td>" );
					for ( dow = 0; dow < 7; dow++ ) { // create date picker days
						daySettings = ( beforeShowDay ?
							beforeShowDay.apply( ( inst.input ? inst.input[ 0 ] : null ), [ printDate ] ) : [ true, "" ] );
						otherMonth = ( printDate.getMonth() !== drawMonth );
						unselectable = ( otherMonth && !selectOtherMonths ) || !daySettings[ 0 ] ||
							( minDate && printDate < minDate ) || ( maxDate && printDate > maxDate );
						tbody += "<td class='" +
							( ( dow + firstDay + 6 ) % 7 >= 5 ? " ui-datepicker-week-end" : "" ) + // highlight weekends
							( otherMonth ? " ui-datepicker-other-month" : "" ) + // highlight days from other months
							( ( printDate.getTime() === selectedDate.getTime() && drawMonth === inst.selectedMonth && inst._keyEvent ) || // user pressed key
							( defaultDate.getTime() === printDate.getTime() && defaultDate.getTime() === selectedDate.getTime() ) ?

							// or defaultDate is current printedDate and defaultDate is selectedDate
							" " + this._dayOverClass : "" ) + // highlight selected day
							( unselectable ? " " + this._unselectableClass + " ui-state-disabled" : "" ) +  // highlight unselectable days
							( otherMonth && !showOtherMonths ? "" : " " + daySettings[ 1 ] + // highlight custom dates
							( printDate.getTime() === currentDate.getTime() ? " " + this._currentClass : "" ) + // highlight selected day
							( printDate.getTime() === today.getTime() ? " ui-datepicker-today" : "" ) ) + "'" + // highlight today (if different)
							( ( !otherMonth || showOtherMonths ) && daySettings[ 2 ] ? " title='" + daySettings[ 2 ].replace( /'/g, "&#39;" ) + "'" : "" ) + // cell title
							( unselectable ? "" : " data-handler='selectDay' data-event='click' data-month='" + printDate.getMonth() + "' data-year='" + printDate.getFullYear() + "'" ) + ">" + // actions
							( otherMonth && !showOtherMonths ? "&#xa0;" : // display for other months
							( unselectable ? "<span class='ui-state-default'>" + printDate.getDate() + "</span>" : "<a class='ui-state-default" +
							( printDate.getTime() === today.getTime() ? " ui-state-highlight" : "" ) +
							( printDate.getTime() === currentDate.getTime() ? " ui-state-active" : "" ) + // highlight selected day
							( otherMonth ? " ui-priority-secondary" : "" ) + // distinguish dates from other months
							"' href='#'>" + printDate.getDate() + "</a>" ) ) + "</td>"; // display selectable date
						printDate.setDate( printDate.getDate() + 1 );
						printDate = this._daylightSavingAdjust( printDate );
					}
					calender += tbody + "</tr>";
				}
				drawMonth++;
				if ( drawMonth > 11 ) {
					drawMonth = 0;
					drawYear++;
				}
				calender += "</tbody></table>" + ( isMultiMonth ? "</div>" +
							( ( numMonths[ 0 ] > 0 && col === numMonths[ 1 ] - 1 ) ? "<div class='ui-datepicker-row-break'></div>" : "" ) : "" );
				group += calender;
			}
			html += group;
		}
		html += buttonPanel;
		inst._keyEvent = false;
		return html;
	},

	/* Generate the month and year header. */
	_generateMonthYearHeader: function( inst, drawMonth, drawYear, minDate, maxDate,
			secondary, monthNames, monthNamesShort ) {

		var inMinYear, inMaxYear, month, years, thisYear, determineYear, year, endYear,
			changeMonth = this._get( inst, "changeMonth" ),
			changeYear = this._get( inst, "changeYear" ),
			showMonthAfterYear = this._get( inst, "showMonthAfterYear" ),
			html = "<div class='ui-datepicker-title'>",
			monthHtml = "";

		// Month selection
		if ( secondary || !changeMonth ) {
			monthHtml += "<span class='ui-datepicker-month'>" + monthNames[ drawMonth ] + "</span>";
		} else {
			inMinYear = ( minDate && minDate.getFullYear() === drawYear );
			inMaxYear = ( maxDate && maxDate.getFullYear() === drawYear );
			monthHtml += "<select class='ui-datepicker-month' data-handler='selectMonth' data-event='change'>";
			for ( month = 0; month < 12; month++ ) {
				if ( ( !inMinYear || month >= minDate.getMonth() ) && ( !inMaxYear || month <= maxDate.getMonth() ) ) {
					monthHtml += "<option value='" + month + "'" +
						( month === drawMonth ? " selected='selected'" : "" ) +
						">" + monthNamesShort[ month ] + "</option>";
				}
			}
			monthHtml += "</select>";
		}

		if ( !showMonthAfterYear ) {
			html += monthHtml + ( secondary || !( changeMonth && changeYear ) ? "&#xa0;" : "" );
		}

		// Year selection
		if ( !inst.yearshtml ) {
			inst.yearshtml = "";
			if ( secondary || !changeYear ) {
				html += "<span class='ui-datepicker-year'>" + drawYear + "</span>";
			} else {

				// determine range of years to display
				years = this._get( inst, "yearRange" ).split( ":" );
				thisYear = new Date().getFullYear();
				determineYear = function( value ) {
					var year = ( value.match( /c[+\-].*/ ) ? drawYear + parseInt( value.substring( 1 ), 10 ) :
						( value.match( /[+\-].*/ ) ? thisYear + parseInt( value, 10 ) :
						parseInt( value, 10 ) ) );
					return ( isNaN( year ) ? thisYear : year );
				};
				year = determineYear( years[ 0 ] );
				endYear = Math.max( year, determineYear( years[ 1 ] || "" ) );
				year = ( minDate ? Math.max( year, minDate.getFullYear() ) : year );
				endYear = ( maxDate ? Math.min( endYear, maxDate.getFullYear() ) : endYear );
				inst.yearshtml += "<select class='ui-datepicker-year' data-handler='selectYear' data-event='change'>";
				for ( ; year <= endYear; year++ ) {
					inst.yearshtml += "<option value='" + year + "'" +
						( year === drawYear ? " selected='selected'" : "" ) +
						">" + year + "</option>";
				}
				inst.yearshtml += "</select>";

				html += inst.yearshtml;
				inst.yearshtml = null;
			}
		}

		html += this._get( inst, "yearSuffix" );
		if ( showMonthAfterYear ) {
			html += ( secondary || !( changeMonth && changeYear ) ? "&#xa0;" : "" ) + monthHtml;
		}
		html += "</div>"; // Close datepicker_header
		return html;
	},

	/* Adjust one of the date sub-fields. */
	_adjustInstDate: function( inst, offset, period ) {
		var year = inst.selectedYear + ( period === "Y" ? offset : 0 ),
			month = inst.selectedMonth + ( period === "M" ? offset : 0 ),
			day = Math.min( inst.selectedDay, this._getDaysInMonth( year, month ) ) + ( period === "D" ? offset : 0 ),
			date = this._restrictMinMax( inst, this._daylightSavingAdjust( new Date( year, month, day ) ) );

		inst.selectedDay = date.getDate();
		inst.drawMonth = inst.selectedMonth = date.getMonth();
		inst.drawYear = inst.selectedYear = date.getFullYear();
		if ( period === "M" || period === "Y" ) {
			this._notifyChange( inst );
		}
	},

	/* Ensure a date is within any min/max bounds. */
	_restrictMinMax: function( inst, date ) {
		var minDate = this._getMinMaxDate( inst, "min" ),
			maxDate = this._getMinMaxDate( inst, "max" ),
			newDate = ( minDate && date < minDate ? minDate : date );
		return ( maxDate && newDate > maxDate ? maxDate : newDate );
	},

	/* Notify change of month/year. */
	_notifyChange: function( inst ) {
		var onChange = this._get( inst, "onChangeMonthYear" );
		if ( onChange ) {
			onChange.apply( ( inst.input ? inst.input[ 0 ] : null ),
				[ inst.selectedYear, inst.selectedMonth + 1, inst ] );
		}
	},

	/* Determine the number of months to show. */
	_getNumberOfMonths: function( inst ) {
		var numMonths = this._get( inst, "numberOfMonths" );
		return ( numMonths == null ? [ 1, 1 ] : ( typeof numMonths === "number" ? [ 1, numMonths ] : numMonths ) );
	},

	/* Determine the current maximum date - ensure no time components are set. */
	_getMinMaxDate: function( inst, minMax ) {
		return this._determineDate( inst, this._get( inst, minMax + "Date" ), null );
	},

	/* Find the number of days in a given month. */
	_getDaysInMonth: function( year, month ) {
		return 32 - this._daylightSavingAdjust( new Date( year, month, 32 ) ).getDate();
	},

	/* Find the day of the week of the first of a month. */
	_getFirstDayOfMonth: function( year, month ) {
		return new Date( year, month, 1 ).getDay();
	},

	/* Determines if we should allow a "next/prev" month display change. */
	_canAdjustMonth: function( inst, offset, curYear, curMonth ) {
		var numMonths = this._getNumberOfMonths( inst ),
			date = this._daylightSavingAdjust( new Date( curYear,
			curMonth + ( offset < 0 ? offset : numMonths[ 0 ] * numMonths[ 1 ] ), 1 ) );

		if ( offset < 0 ) {
			date.setDate( this._getDaysInMonth( date.getFullYear(), date.getMonth() ) );
		}
		return this._isInRange( inst, date );
	},

	/* Is the given date in the accepted range? */
	_isInRange: function( inst, date ) {
		var yearSplit, currentYear,
			minDate = this._getMinMaxDate( inst, "min" ),
			maxDate = this._getMinMaxDate( inst, "max" ),
			minYear = null,
			maxYear = null,
			years = this._get( inst, "yearRange" );
			if ( years ) {
				yearSplit = years.split( ":" );
				currentYear = new Date().getFullYear();
				minYear = parseInt( yearSplit[ 0 ], 10 );
				maxYear = parseInt( yearSplit[ 1 ], 10 );
				if ( yearSplit[ 0 ].match( /[+\-].*/ ) ) {
					minYear += currentYear;
				}
				if ( yearSplit[ 1 ].match( /[+\-].*/ ) ) {
					maxYear += currentYear;
				}
			}

		return ( ( !minDate || date.getTime() >= minDate.getTime() ) &&
			( !maxDate || date.getTime() <= maxDate.getTime() ) &&
			( !minYear || date.getFullYear() >= minYear ) &&
			( !maxYear || date.getFullYear() <= maxYear ) );
	},

	/* Provide the configuration settings for formatting/parsing. */
	_getFormatConfig: function( inst ) {
		var shortYearCutoff = this._get( inst, "shortYearCutoff" );
		shortYearCutoff = ( typeof shortYearCutoff !== "string" ? shortYearCutoff :
			new Date().getFullYear() % 100 + parseInt( shortYearCutoff, 10 ) );
		return { shortYearCutoff: shortYearCutoff,
			dayNamesShort: this._get( inst, "dayNamesShort" ), dayNames: this._get( inst, "dayNames" ),
			monthNamesShort: this._get( inst, "monthNamesShort" ), monthNames: this._get( inst, "monthNames" ) };
	},

	/* Format the given date for display. */
	_formatDate: function( inst, day, month, year ) {
		if ( !day ) {
			inst.currentDay = inst.selectedDay;
			inst.currentMonth = inst.selectedMonth;
			inst.currentYear = inst.selectedYear;
		}
		var date = ( day ? ( typeof day === "object" ? day :
			this._daylightSavingAdjust( new Date( year, month, day ) ) ) :
			this._daylightSavingAdjust( new Date( inst.currentYear, inst.currentMonth, inst.currentDay ) ) );
		return this.formatDate( this._get( inst, "dateFormat" ), date, this._getFormatConfig( inst ) );
	}
} );

/*
 * Bind hover events for datepicker elements.
 * Done via delegate so the binding only occurs once in the lifetime of the parent div.
 * Global datepicker_instActive, set by _updateDatepicker allows the handlers to find their way back to the active picker.
 */
function datepicker_bindHover( dpDiv ) {
	var selector = "button, .ui-datepicker-prev, .ui-datepicker-next, .ui-datepicker-calendar td a";
	return dpDiv.on( "mouseout", selector, function() {
			$( this ).removeClass( "ui-state-hover" );
			if ( this.className.indexOf( "ui-datepicker-prev" ) !== -1 ) {
				$( this ).removeClass( "ui-datepicker-prev-hover" );
			}
			if ( this.className.indexOf( "ui-datepicker-next" ) !== -1 ) {
				$( this ).removeClass( "ui-datepicker-next-hover" );
			}
		} )
		.on( "mouseover", selector, datepicker_handleMouseover );
}

function datepicker_handleMouseover() {
	if ( !$.datepicker._isDisabledDatepicker( datepicker_instActive.inline ? datepicker_instActive.dpDiv.parent()[ 0 ] : datepicker_instActive.input[ 0 ] ) ) {
		$( this ).parents( ".ui-datepicker-calendar" ).find( "a" ).removeClass( "ui-state-hover" );
		$( this ).addClass( "ui-state-hover" );
		if ( this.className.indexOf( "ui-datepicker-prev" ) !== -1 ) {
			$( this ).addClass( "ui-datepicker-prev-hover" );
		}
		if ( this.className.indexOf( "ui-datepicker-next" ) !== -1 ) {
			$( this ).addClass( "ui-datepicker-next-hover" );
		}
	}
}

/* jQuery extend now ignores nulls! */
function datepicker_extendRemove( target, props ) {
	$.extend( target, props );
	for ( var name in props ) {
		if ( props[ name ] == null ) {
			target[ name ] = props[ name ];
		}
	}
	return target;
}

/* Invoke the datepicker functionality.
   @param  options  string - a command, optionally followed by additional parameters or
					Object - settings for attaching new datepicker functionality
   @return  jQuery object */
$.fn.datepicker = function( options ) {

	/* Verify an empty collection wasn't passed - Fixes #6976 */
	if ( !this.length ) {
		return this;
	}

	/* Initialise the date picker. */
	if ( !$.datepicker.initialized ) {
		$( document ).on( "mousedown", $.datepicker._checkExternalClick );
		$.datepicker.initialized = true;
	}

	/* Append datepicker main container to body if not exist. */
	if ( $( "#" + $.datepicker._mainDivId ).length === 0 ) {
		$( "body" ).append( $.datepicker.dpDiv );
	}

	var otherArgs = Array.prototype.slice.call( arguments, 1 );
	if ( typeof options === "string" && ( options === "isDisabled" || options === "getDate" || options === "widget" ) ) {
		return $.datepicker[ "_" + options + "Datepicker" ].
			apply( $.datepicker, [ this[ 0 ] ].concat( otherArgs ) );
	}
	if ( options === "option" && arguments.length === 2 && typeof arguments[ 1 ] === "string" ) {
		return $.datepicker[ "_" + options + "Datepicker" ].
			apply( $.datepicker, [ this[ 0 ] ].concat( otherArgs ) );
	}
	return this.each( function() {
		typeof options === "string" ?
			$.datepicker[ "_" + options + "Datepicker" ].
				apply( $.datepicker, [ this ].concat( otherArgs ) ) :
			$.datepicker._attachDatepicker( this, options );
	} );
};

$.datepicker = new Datepicker(); // singleton instance
$.datepicker.initialized = false;
$.datepicker.uuid = new Date().getTime();
$.datepicker.version = "1.12.1";

var widgetsDatepicker = $.datepicker;




// This file is deprecated
var ie = $.ui.ie = !!/msie [\w.]+/.exec( navigator.userAgent.toLowerCase() );

/*!
 * jQuery UI Mouse 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Mouse
//>>group: Widgets
//>>description: Abstracts mouse-based interactions to assist in creating certain widgets.
//>>docs: http://api.jqueryui.com/mouse/



var mouseHandled = false;
$( document ).on( "mouseup", function() {
	mouseHandled = false;
} );

var widgetsMouse = $.widget( "ui.mouse", {
	version: "1.12.1",
	options: {
		cancel: "input, textarea, button, select, option",
		distance: 1,
		delay: 0
	},
	_mouseInit: function() {
		var that = this;

		this.element
			.on( "mousedown." + this.widgetName, function( event ) {
				return that._mouseDown( event );
			} )
			.on( "click." + this.widgetName, function( event ) {
				if ( true === $.data( event.target, that.widgetName + ".preventClickEvent" ) ) {
					$.removeData( event.target, that.widgetName + ".preventClickEvent" );
					event.stopImmediatePropagation();
					return false;
				}
			} );

		this.started = false;
	},

	// TODO: make sure destroying one instance of mouse doesn't mess with
	// other instances of mouse
	_mouseDestroy: function() {
		this.element.off( "." + this.widgetName );
		if ( this._mouseMoveDelegate ) {
			this.document
				.off( "mousemove." + this.widgetName, this._mouseMoveDelegate )
				.off( "mouseup." + this.widgetName, this._mouseUpDelegate );
		}
	},

	_mouseDown: function( event ) {

		// don't let more than one widget handle mouseStart
		if ( mouseHandled ) {
			return;
		}

		this._mouseMoved = false;

		// We may have missed mouseup (out of window)
		( this._mouseStarted && this._mouseUp( event ) );

		this._mouseDownEvent = event;

		var that = this,
			btnIsLeft = ( event.which === 1 ),

			// event.target.nodeName works around a bug in IE 8 with
			// disabled inputs (#7620)
			elIsCancel = ( typeof this.options.cancel === "string" && event.target.nodeName ?
				$( event.target ).closest( this.options.cancel ).length : false );
		if ( !btnIsLeft || elIsCancel || !this._mouseCapture( event ) ) {
			return true;
		}

		this.mouseDelayMet = !this.options.delay;
		if ( !this.mouseDelayMet ) {
			this._mouseDelayTimer = setTimeout( function() {
				that.mouseDelayMet = true;
			}, this.options.delay );
		}

		if ( this._mouseDistanceMet( event ) && this._mouseDelayMet( event ) ) {
			this._mouseStarted = ( this._mouseStart( event ) !== false );
			if ( !this._mouseStarted ) {
				event.preventDefault();
				return true;
			}
		}

		// Click event may never have fired (Gecko & Opera)
		if ( true === $.data( event.target, this.widgetName + ".preventClickEvent" ) ) {
			$.removeData( event.target, this.widgetName + ".preventClickEvent" );
		}

		// These delegates are required to keep context
		this._mouseMoveDelegate = function( event ) {
			return that._mouseMove( event );
		};
		this._mouseUpDelegate = function( event ) {
			return that._mouseUp( event );
		};

		this.document
			.on( "mousemove." + this.widgetName, this._mouseMoveDelegate )
			.on( "mouseup." + this.widgetName, this._mouseUpDelegate );

		event.preventDefault();

		mouseHandled = true;
		return true;
	},

	_mouseMove: function( event ) {

		// Only check for mouseups outside the document if you've moved inside the document
		// at least once. This prevents the firing of mouseup in the case of IE<9, which will
		// fire a mousemove event if content is placed under the cursor. See #7778
		// Support: IE <9
		if ( this._mouseMoved ) {

			// IE mouseup check - mouseup happened when mouse was out of window
			if ( $.ui.ie && ( !document.documentMode || document.documentMode < 9 ) &&
					!event.button ) {
				return this._mouseUp( event );

			// Iframe mouseup check - mouseup occurred in another document
			} else if ( !event.which ) {

				// Support: Safari <=8 - 9
				// Safari sets which to 0 if you press any of the following keys
				// during a drag (#14461)
				if ( event.originalEvent.altKey || event.originalEvent.ctrlKey ||
						event.originalEvent.metaKey || event.originalEvent.shiftKey ) {
					this.ignoreMissingWhich = true;
				} else if ( !this.ignoreMissingWhich ) {
					return this._mouseUp( event );
				}
			}
		}

		if ( event.which || event.button ) {
			this._mouseMoved = true;
		}

		if ( this._mouseStarted ) {
			this._mouseDrag( event );
			return event.preventDefault();
		}

		if ( this._mouseDistanceMet( event ) && this._mouseDelayMet( event ) ) {
			this._mouseStarted =
				( this._mouseStart( this._mouseDownEvent, event ) !== false );
			( this._mouseStarted ? this._mouseDrag( event ) : this._mouseUp( event ) );
		}

		return !this._mouseStarted;
	},

	_mouseUp: function( event ) {
		this.document
			.off( "mousemove." + this.widgetName, this._mouseMoveDelegate )
			.off( "mouseup." + this.widgetName, this._mouseUpDelegate );

		if ( this._mouseStarted ) {
			this._mouseStarted = false;

			if ( event.target === this._mouseDownEvent.target ) {
				$.data( event.target, this.widgetName + ".preventClickEvent", true );
			}

			this._mouseStop( event );
		}

		if ( this._mouseDelayTimer ) {
			clearTimeout( this._mouseDelayTimer );
			delete this._mouseDelayTimer;
		}

		this.ignoreMissingWhich = false;
		mouseHandled = false;
		event.preventDefault();
	},

	_mouseDistanceMet: function( event ) {
		return ( Math.max(
				Math.abs( this._mouseDownEvent.pageX - event.pageX ),
				Math.abs( this._mouseDownEvent.pageY - event.pageY )
			) >= this.options.distance
		);
	},

	_mouseDelayMet: function( /* event */ ) {
		return this.mouseDelayMet;
	},

	// These are placeholder methods, to be overriden by extending plugin
	_mouseStart: function( /* event */ ) {},
	_mouseDrag: function( /* event */ ) {},
	_mouseStop: function( /* event */ ) {},
	_mouseCapture: function( /* event */ ) { return true; }
} );




// $.ui.plugin is deprecated. Use $.widget() extensions instead.
var plugin = $.ui.plugin = {
	add: function( module, option, set ) {
		var i,
			proto = $.ui[ module ].prototype;
		for ( i in set ) {
			proto.plugins[ i ] = proto.plugins[ i ] || [];
			proto.plugins[ i ].push( [ option, set[ i ] ] );
		}
	},
	call: function( instance, name, args, allowDisconnected ) {
		var i,
			set = instance.plugins[ name ];

		if ( !set ) {
			return;
		}

		if ( !allowDisconnected && ( !instance.element[ 0 ].parentNode ||
				instance.element[ 0 ].parentNode.nodeType === 11 ) ) {
			return;
		}

		for ( i = 0; i < set.length; i++ ) {
			if ( instance.options[ set[ i ][ 0 ] ] ) {
				set[ i ][ 1 ].apply( instance.element, args );
			}
		}
	}
};



var safeBlur = $.ui.safeBlur = function( element ) {

	// Support: IE9 - 10 only
	// If the <body> is blurred, IE will switch windows, see #9420
	if ( element && element.nodeName.toLowerCase() !== "body" ) {
		$( element ).trigger( "blur" );
	}
};


/*!
 * jQuery UI Draggable 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Draggable
//>>group: Interactions
//>>description: Enables dragging functionality for any element.
//>>docs: http://api.jqueryui.com/draggable/
//>>demos: http://jqueryui.com/draggable/
//>>css.structure: ../../themes/base/draggable.css



$.widget( "ui.draggable", $.ui.mouse, {
	version: "1.12.1",
	widgetEventPrefix: "drag",
	options: {
		addClasses: true,
		appendTo: "parent",
		axis: false,
		connectToSortable: false,
		containment: false,
		cursor: "auto",
		cursorAt: false,
		grid: false,
		handle: false,
		helper: "original",
		iframeFix: false,
		opacity: false,
		refreshPositions: false,
		revert: false,
		revertDuration: 500,
		scope: "default",
		scroll: true,
		scrollSensitivity: 20,
		scrollSpeed: 20,
		snap: false,
		snapMode: "both",
		snapTolerance: 20,
		stack: false,
		zIndex: false,

		// Callbacks
		drag: null,
		start: null,
		stop: null
	},
	_create: function() {

		if ( this.options.helper === "original" ) {
			this._setPositionRelative();
		}
		if ( this.options.addClasses ) {
			this._addClass( "ui-draggable" );
		}
		this._setHandleClassName();

		this._mouseInit();
	},

	_setOption: function( key, value ) {
		this._super( key, value );
		if ( key === "handle" ) {
			this._removeHandleClassName();
			this._setHandleClassName();
		}
	},

	_destroy: function() {
		if ( ( this.helper || this.element ).is( ".ui-draggable-dragging" ) ) {
			this.destroyOnClear = true;
			return;
		}
		this._removeHandleClassName();
		this._mouseDestroy();
	},

	_mouseCapture: function( event ) {
		var o = this.options;

		// Among others, prevent a drag on a resizable-handle
		if ( this.helper || o.disabled ||
				$( event.target ).closest( ".ui-resizable-handle" ).length > 0 ) {
			return false;
		}

		//Quit if we're not on a valid handle
		this.handle = this._getHandle( event );
		if ( !this.handle ) {
			return false;
		}

		this._blurActiveElement( event );

		this._blockFrames( o.iframeFix === true ? "iframe" : o.iframeFix );

		return true;

	},

	_blockFrames: function( selector ) {
		this.iframeBlocks = this.document.find( selector ).map( function() {
			var iframe = $( this );

			return $( "<div>" )
				.css( "position", "absolute" )
				.appendTo( iframe.parent() )
				.outerWidth( iframe.outerWidth() )
				.outerHeight( iframe.outerHeight() )
				.offset( iframe.offset() )[ 0 ];
		} );
	},

	_unblockFrames: function() {
		if ( this.iframeBlocks ) {
			this.iframeBlocks.remove();
			delete this.iframeBlocks;
		}
	},

	_blurActiveElement: function( event ) {
		var activeElement = $.ui.safeActiveElement( this.document[ 0 ] ),
			target = $( event.target );

		// Don't blur if the event occurred on an element that is within
		// the currently focused element
		// See #10527, #12472
		if ( target.closest( activeElement ).length ) {
			return;
		}

		// Blur any element that currently has focus, see #4261
		$.ui.safeBlur( activeElement );
	},

	_mouseStart: function( event ) {

		var o = this.options;

		//Create and append the visible helper
		this.helper = this._createHelper( event );

		this._addClass( this.helper, "ui-draggable-dragging" );

		//Cache the helper size
		this._cacheHelperProportions();

		//If ddmanager is used for droppables, set the global draggable
		if ( $.ui.ddmanager ) {
			$.ui.ddmanager.current = this;
		}

		/*
		 * - Position generation -
		 * This block generates everything position related - it's the core of draggables.
		 */

		//Cache the margins of the original element
		this._cacheMargins();

		//Store the helper's css position
		this.cssPosition = this.helper.css( "position" );
		this.scrollParent = this.helper.scrollParent( true );
		this.offsetParent = this.helper.offsetParent();
		this.hasFixedAncestor = this.helper.parents().filter( function() {
				return $( this ).css( "position" ) === "fixed";
			} ).length > 0;

		//The element's absolute position on the page minus margins
		this.positionAbs = this.element.offset();
		this._refreshOffsets( event );

		//Generate the original position
		this.originalPosition = this.position = this._generatePosition( event, false );
		this.originalPageX = event.pageX;
		this.originalPageY = event.pageY;

		//Adjust the mouse offset relative to the helper if "cursorAt" is supplied
		( o.cursorAt && this._adjustOffsetFromHelper( o.cursorAt ) );

		//Set a containment if given in the options
		this._setContainment();

		//Trigger event + callbacks
		if ( this._trigger( "start", event ) === false ) {
			this._clear();
			return false;
		}

		//Recache the helper size
		this._cacheHelperProportions();

		//Prepare the droppable offsets
		if ( $.ui.ddmanager && !o.dropBehaviour ) {
			$.ui.ddmanager.prepareOffsets( this, event );
		}

		// Execute the drag once - this causes the helper not to be visible before getting its
		// correct position
		this._mouseDrag( event, true );

		// If the ddmanager is used for droppables, inform the manager that dragging has started
		// (see #5003)
		if ( $.ui.ddmanager ) {
			$.ui.ddmanager.dragStart( this, event );
		}

		return true;
	},

	_refreshOffsets: function( event ) {
		this.offset = {
			top: this.positionAbs.top - this.margins.top,
			left: this.positionAbs.left - this.margins.left,
			scroll: false,
			parent: this._getParentOffset(),
			relative: this._getRelativeOffset()
		};

		this.offset.click = {
			left: event.pageX - this.offset.left,
			top: event.pageY - this.offset.top
		};
	},

	_mouseDrag: function( event, noPropagation ) {

		// reset any necessary cached properties (see #5009)
		if ( this.hasFixedAncestor ) {
			this.offset.parent = this._getParentOffset();
		}

		//Compute the helpers position
		this.position = this._generatePosition( event, true );
		this.positionAbs = this._convertPositionTo( "absolute" );

		//Call plugins and callbacks and use the resulting position if something is returned
		if ( !noPropagation ) {
			var ui = this._uiHash();
			if ( this._trigger( "drag", event, ui ) === false ) {
				this._mouseUp( new $.Event( "mouseup", event ) );
				return false;
			}
			this.position = ui.position;
		}

		this.helper[ 0 ].style.left = this.position.left + "px";
		this.helper[ 0 ].style.top = this.position.top + "px";

		if ( $.ui.ddmanager ) {
			$.ui.ddmanager.drag( this, event );
		}

		return false;
	},

	_mouseStop: function( event ) {

		//If we are using droppables, inform the manager about the drop
		var that = this,
			dropped = false;
		if ( $.ui.ddmanager && !this.options.dropBehaviour ) {
			dropped = $.ui.ddmanager.drop( this, event );
		}

		//if a drop comes from outside (a sortable)
		if ( this.dropped ) {
			dropped = this.dropped;
			this.dropped = false;
		}

		if ( ( this.options.revert === "invalid" && !dropped ) ||
				( this.options.revert === "valid" && dropped ) ||
				this.options.revert === true || ( $.isFunction( this.options.revert ) &&
				this.options.revert.call( this.element, dropped ) )
		) {
			$( this.helper ).animate(
				this.originalPosition,
				parseInt( this.options.revertDuration, 10 ),
				function() {
					if ( that._trigger( "stop", event ) !== false ) {
						that._clear();
					}
				}
			);
		} else {
			if ( this._trigger( "stop", event ) !== false ) {
				this._clear();
			}
		}

		return false;
	},

	_mouseUp: function( event ) {
		this._unblockFrames();

		// If the ddmanager is used for droppables, inform the manager that dragging has stopped
		// (see #5003)
		if ( $.ui.ddmanager ) {
			$.ui.ddmanager.dragStop( this, event );
		}

		// Only need to focus if the event occurred on the draggable itself, see #10527
		if ( this.handleElement.is( event.target ) ) {

			// The interaction is over; whether or not the click resulted in a drag,
			// focus the element
			this.element.trigger( "focus" );
		}

		return $.ui.mouse.prototype._mouseUp.call( this, event );
	},

	cancel: function() {

		if ( this.helper.is( ".ui-draggable-dragging" ) ) {
			this._mouseUp( new $.Event( "mouseup", { target: this.element[ 0 ] } ) );
		} else {
			this._clear();
		}

		return this;

	},

	_getHandle: function( event ) {
		return this.options.handle ?
			!!$( event.target ).closest( this.element.find( this.options.handle ) ).length :
			true;
	},

	_setHandleClassName: function() {
		this.handleElement = this.options.handle ?
			this.element.find( this.options.handle ) : this.element;
		this._addClass( this.handleElement, "ui-draggable-handle" );
	},

	_removeHandleClassName: function() {
		this._removeClass( this.handleElement, "ui-draggable-handle" );
	},

	_createHelper: function( event ) {

		var o = this.options,
			helperIsFunction = $.isFunction( o.helper ),
			helper = helperIsFunction ?
				$( o.helper.apply( this.element[ 0 ], [ event ] ) ) :
				( o.helper === "clone" ?
					this.element.clone().removeAttr( "id" ) :
					this.element );

		if ( !helper.parents( "body" ).length ) {
			helper.appendTo( ( o.appendTo === "parent" ?
				this.element[ 0 ].parentNode :
				o.appendTo ) );
		}

		// Http://bugs.jqueryui.com/ticket/9446
		// a helper function can return the original element
		// which wouldn't have been set to relative in _create
		if ( helperIsFunction && helper[ 0 ] === this.element[ 0 ] ) {
			this._setPositionRelative();
		}

		if ( helper[ 0 ] !== this.element[ 0 ] &&
				!( /(fixed|absolute)/ ).test( helper.css( "position" ) ) ) {
			helper.css( "position", "absolute" );
		}

		return helper;

	},

	_setPositionRelative: function() {
		if ( !( /^(?:r|a|f)/ ).test( this.element.css( "position" ) ) ) {
			this.element[ 0 ].style.position = "relative";
		}
	},

	_adjustOffsetFromHelper: function( obj ) {
		if ( typeof obj === "string" ) {
			obj = obj.split( " " );
		}
		if ( $.isArray( obj ) ) {
			obj = { left: +obj[ 0 ], top: +obj[ 1 ] || 0 };
		}
		if ( "left" in obj ) {
			this.offset.click.left = obj.left + this.margins.left;
		}
		if ( "right" in obj ) {
			this.offset.click.left = this.helperProportions.width - obj.right + this.margins.left;
		}
		if ( "top" in obj ) {
			this.offset.click.top = obj.top + this.margins.top;
		}
		if ( "bottom" in obj ) {
			this.offset.click.top = this.helperProportions.height - obj.bottom + this.margins.top;
		}
	},

	_isRootNode: function( element ) {
		return ( /(html|body)/i ).test( element.tagName ) || element === this.document[ 0 ];
	},

	_getParentOffset: function() {

		//Get the offsetParent and cache its position
		var po = this.offsetParent.offset(),
			document = this.document[ 0 ];

		// This is a special case where we need to modify a offset calculated on start, since the
		// following happened:
		// 1. The position of the helper is absolute, so it's position is calculated based on the
		// next positioned parent
		// 2. The actual offset parent is a child of the scroll parent, and the scroll parent isn't
		// the document, which means that the scroll is included in the initial calculation of the
		// offset of the parent, and never recalculated upon drag
		if ( this.cssPosition === "absolute" && this.scrollParent[ 0 ] !== document &&
				$.contains( this.scrollParent[ 0 ], this.offsetParent[ 0 ] ) ) {
			po.left += this.scrollParent.scrollLeft();
			po.top += this.scrollParent.scrollTop();
		}

		if ( this._isRootNode( this.offsetParent[ 0 ] ) ) {
			po = { top: 0, left: 0 };
		}

		return {
			top: po.top + ( parseInt( this.offsetParent.css( "borderTopWidth" ), 10 ) || 0 ),
			left: po.left + ( parseInt( this.offsetParent.css( "borderLeftWidth" ), 10 ) || 0 )
		};

	},

	_getRelativeOffset: function() {
		if ( this.cssPosition !== "relative" ) {
			return { top: 0, left: 0 };
		}

		var p = this.element.position(),
			scrollIsRootNode = this._isRootNode( this.scrollParent[ 0 ] );

		return {
			top: p.top - ( parseInt( this.helper.css( "top" ), 10 ) || 0 ) +
				( !scrollIsRootNode ? this.scrollParent.scrollTop() : 0 ),
			left: p.left - ( parseInt( this.helper.css( "left" ), 10 ) || 0 ) +
				( !scrollIsRootNode ? this.scrollParent.scrollLeft() : 0 )
		};

	},

	_cacheMargins: function() {
		this.margins = {
			left: ( parseInt( this.element.css( "marginLeft" ), 10 ) || 0 ),
			top: ( parseInt( this.element.css( "marginTop" ), 10 ) || 0 ),
			right: ( parseInt( this.element.css( "marginRight" ), 10 ) || 0 ),
			bottom: ( parseInt( this.element.css( "marginBottom" ), 10 ) || 0 )
		};
	},

	_cacheHelperProportions: function() {
		this.helperProportions = {
			width: this.helper.outerWidth(),
			height: this.helper.outerHeight()
		};
	},

	_setContainment: function() {

		var isUserScrollable, c, ce,
			o = this.options,
			document = this.document[ 0 ];

		this.relativeContainer = null;

		if ( !o.containment ) {
			this.containment = null;
			return;
		}

		if ( o.containment === "window" ) {
			this.containment = [
				$( window ).scrollLeft() - this.offset.relative.left - this.offset.parent.left,
				$( window ).scrollTop() - this.offset.relative.top - this.offset.parent.top,
				$( window ).scrollLeft() + $( window ).width() -
					this.helperProportions.width - this.margins.left,
				$( window ).scrollTop() +
					( $( window ).height() || document.body.parentNode.scrollHeight ) -
					this.helperProportions.height - this.margins.top
			];
			return;
		}

		if ( o.containment === "document" ) {
			this.containment = [
				0,
				0,
				$( document ).width() - this.helperProportions.width - this.margins.left,
				( $( document ).height() || document.body.parentNode.scrollHeight ) -
					this.helperProportions.height - this.margins.top
			];
			return;
		}

		if ( o.containment.constructor === Array ) {
			this.containment = o.containment;
			return;
		}

		if ( o.containment === "parent" ) {
			o.containment = this.helper[ 0 ].parentNode;
		}

		c = $( o.containment );
		ce = c[ 0 ];

		if ( !ce ) {
			return;
		}

		isUserScrollable = /(scroll|auto)/.test( c.css( "overflow" ) );

		this.containment = [
			( parseInt( c.css( "borderLeftWidth" ), 10 ) || 0 ) +
				( parseInt( c.css( "paddingLeft" ), 10 ) || 0 ),
			( parseInt( c.css( "borderTopWidth" ), 10 ) || 0 ) +
				( parseInt( c.css( "paddingTop" ), 10 ) || 0 ),
			( isUserScrollable ? Math.max( ce.scrollWidth, ce.offsetWidth ) : ce.offsetWidth ) -
				( parseInt( c.css( "borderRightWidth" ), 10 ) || 0 ) -
				( parseInt( c.css( "paddingRight" ), 10 ) || 0 ) -
				this.helperProportions.width -
				this.margins.left -
				this.margins.right,
			( isUserScrollable ? Math.max( ce.scrollHeight, ce.offsetHeight ) : ce.offsetHeight ) -
				( parseInt( c.css( "borderBottomWidth" ), 10 ) || 0 ) -
				( parseInt( c.css( "paddingBottom" ), 10 ) || 0 ) -
				this.helperProportions.height -
				this.margins.top -
				this.margins.bottom
		];
		this.relativeContainer = c;
	},

	_convertPositionTo: function( d, pos ) {

		if ( !pos ) {
			pos = this.position;
		}

		var mod = d === "absolute" ? 1 : -1,
			scrollIsRootNode = this._isRootNode( this.scrollParent[ 0 ] );

		return {
			top: (

				// The absolute mouse position
				pos.top	+

				// Only for relative positioned nodes: Relative offset from element to offset parent
				this.offset.relative.top * mod +

				// The offsetParent's offset without borders (offset + border)
				this.offset.parent.top * mod -
				( ( this.cssPosition === "fixed" ?
					-this.offset.scroll.top :
					( scrollIsRootNode ? 0 : this.offset.scroll.top ) ) * mod )
			),
			left: (

				// The absolute mouse position
				pos.left +

				// Only for relative positioned nodes: Relative offset from element to offset parent
				this.offset.relative.left * mod +

				// The offsetParent's offset without borders (offset + border)
				this.offset.parent.left * mod	-
				( ( this.cssPosition === "fixed" ?
					-this.offset.scroll.left :
					( scrollIsRootNode ? 0 : this.offset.scroll.left ) ) * mod )
			)
		};

	},

	_generatePosition: function( event, constrainPosition ) {

		var containment, co, top, left,
			o = this.options,
			scrollIsRootNode = this._isRootNode( this.scrollParent[ 0 ] ),
			pageX = event.pageX,
			pageY = event.pageY;

		// Cache the scroll
		if ( !scrollIsRootNode || !this.offset.scroll ) {
			this.offset.scroll = {
				top: this.scrollParent.scrollTop(),
				left: this.scrollParent.scrollLeft()
			};
		}

		/*
		 * - Position constraining -
		 * Constrain the position to a mix of grid, containment.
		 */

		// If we are not dragging yet, we won't check for options
		if ( constrainPosition ) {
			if ( this.containment ) {
				if ( this.relativeContainer ) {
					co = this.relativeContainer.offset();
					containment = [
						this.containment[ 0 ] + co.left,
						this.containment[ 1 ] + co.top,
						this.containment[ 2 ] + co.left,
						this.containment[ 3 ] + co.top
					];
				} else {
					containment = this.containment;
				}

				if ( event.pageX - this.offset.click.left < containment[ 0 ] ) {
					pageX = containment[ 0 ] + this.offset.click.left;
				}
				if ( event.pageY - this.offset.click.top < containment[ 1 ] ) {
					pageY = containment[ 1 ] + this.offset.click.top;
				}
				if ( event.pageX - this.offset.click.left > containment[ 2 ] ) {
					pageX = containment[ 2 ] + this.offset.click.left;
				}
				if ( event.pageY - this.offset.click.top > containment[ 3 ] ) {
					pageY = containment[ 3 ] + this.offset.click.top;
				}
			}

			if ( o.grid ) {

				//Check for grid elements set to 0 to prevent divide by 0 error causing invalid
				// argument errors in IE (see ticket #6950)
				top = o.grid[ 1 ] ? this.originalPageY + Math.round( ( pageY -
					this.originalPageY ) / o.grid[ 1 ] ) * o.grid[ 1 ] : this.originalPageY;
				pageY = containment ? ( ( top - this.offset.click.top >= containment[ 1 ] ||
					top - this.offset.click.top > containment[ 3 ] ) ?
						top :
						( ( top - this.offset.click.top >= containment[ 1 ] ) ?
							top - o.grid[ 1 ] : top + o.grid[ 1 ] ) ) : top;

				left = o.grid[ 0 ] ? this.originalPageX +
					Math.round( ( pageX - this.originalPageX ) / o.grid[ 0 ] ) * o.grid[ 0 ] :
					this.originalPageX;
				pageX = containment ? ( ( left - this.offset.click.left >= containment[ 0 ] ||
					left - this.offset.click.left > containment[ 2 ] ) ?
						left :
						( ( left - this.offset.click.left >= containment[ 0 ] ) ?
							left - o.grid[ 0 ] : left + o.grid[ 0 ] ) ) : left;
			}

			if ( o.axis === "y" ) {
				pageX = this.originalPageX;
			}

			if ( o.axis === "x" ) {
				pageY = this.originalPageY;
			}
		}

		return {
			top: (

				// The absolute mouse position
				pageY -

				// Click offset (relative to the element)
				this.offset.click.top -

				// Only for relative positioned nodes: Relative offset from element to offset parent
				this.offset.relative.top -

				// The offsetParent's offset without borders (offset + border)
				this.offset.parent.top +
				( this.cssPosition === "fixed" ?
					-this.offset.scroll.top :
					( scrollIsRootNode ? 0 : this.offset.scroll.top ) )
			),
			left: (

				// The absolute mouse position
				pageX -

				// Click offset (relative to the element)
				this.offset.click.left -

				// Only for relative positioned nodes: Relative offset from element to offset parent
				this.offset.relative.left -

				// The offsetParent's offset without borders (offset + border)
				this.offset.parent.left +
				( this.cssPosition === "fixed" ?
					-this.offset.scroll.left :
					( scrollIsRootNode ? 0 : this.offset.scroll.left ) )
			)
		};

	},

	_clear: function() {
		this._removeClass( this.helper, "ui-draggable-dragging" );
		if ( this.helper[ 0 ] !== this.element[ 0 ] && !this.cancelHelperRemoval ) {
			this.helper.remove();
		}
		this.helper = null;
		this.cancelHelperRemoval = false;
		if ( this.destroyOnClear ) {
			this.destroy();
		}
	},

	// From now on bulk stuff - mainly helpers

	_trigger: function( type, event, ui ) {
		ui = ui || this._uiHash();
		$.ui.plugin.call( this, type, [ event, ui, this ], true );

		// Absolute position and offset (see #6884 ) have to be recalculated after plugins
		if ( /^(drag|start|stop)/.test( type ) ) {
			this.positionAbs = this._convertPositionTo( "absolute" );
			ui.offset = this.positionAbs;
		}
		return $.Widget.prototype._trigger.call( this, type, event, ui );
	},

	plugins: {},

	_uiHash: function() {
		return {
			helper: this.helper,
			position: this.position,
			originalPosition: this.originalPosition,
			offset: this.positionAbs
		};
	}

} );

$.ui.plugin.add( "draggable", "connectToSortable", {
	start: function( event, ui, draggable ) {
		var uiSortable = $.extend( {}, ui, {
			item: draggable.element
		} );

		draggable.sortables = [];
		$( draggable.options.connectToSortable ).each( function() {
			var sortable = $( this ).sortable( "instance" );

			if ( sortable && !sortable.options.disabled ) {
				draggable.sortables.push( sortable );

				// RefreshPositions is called at drag start to refresh the containerCache
				// which is used in drag. This ensures it's initialized and synchronized
				// with any changes that might have happened on the page since initialization.
				sortable.refreshPositions();
				sortable._trigger( "activate", event, uiSortable );
			}
		} );
	},
	stop: function( event, ui, draggable ) {
		var uiSortable = $.extend( {}, ui, {
			item: draggable.element
		} );

		draggable.cancelHelperRemoval = false;

		$.each( draggable.sortables, function() {
			var sortable = this;

			if ( sortable.isOver ) {
				sortable.isOver = 0;

				// Allow this sortable to handle removing the helper
				draggable.cancelHelperRemoval = true;
				sortable.cancelHelperRemoval = false;

				// Use _storedCSS To restore properties in the sortable,
				// as this also handles revert (#9675) since the draggable
				// may have modified them in unexpected ways (#8809)
				sortable._storedCSS = {
					position: sortable.placeholder.css( "position" ),
					top: sortable.placeholder.css( "top" ),
					left: sortable.placeholder.css( "left" )
				};

				sortable._mouseStop( event );

				// Once drag has ended, the sortable should return to using
				// its original helper, not the shared helper from draggable
				sortable.options.helper = sortable.options._helper;
			} else {

				// Prevent this Sortable from removing the helper.
				// However, don't set the draggable to remove the helper
				// either as another connected Sortable may yet handle the removal.
				sortable.cancelHelperRemoval = true;

				sortable._trigger( "deactivate", event, uiSortable );
			}
		} );
	},
	drag: function( event, ui, draggable ) {
		$.each( draggable.sortables, function() {
			var innermostIntersecting = false,
				sortable = this;

			// Copy over variables that sortable's _intersectsWith uses
			sortable.positionAbs = draggable.positionAbs;
			sortable.helperProportions = draggable.helperProportions;
			sortable.offset.click = draggable.offset.click;

			if ( sortable._intersectsWith( sortable.containerCache ) ) {
				innermostIntersecting = true;

				$.each( draggable.sortables, function() {

					// Copy over variables that sortable's _intersectsWith uses
					this.positionAbs = draggable.positionAbs;
					this.helperProportions = draggable.helperProportions;
					this.offset.click = draggable.offset.click;

					if ( this !== sortable &&
							this._intersectsWith( this.containerCache ) &&
							$.contains( sortable.element[ 0 ], this.element[ 0 ] ) ) {
						innermostIntersecting = false;
					}

					return innermostIntersecting;
				} );
			}

			if ( innermostIntersecting ) {

				// If it intersects, we use a little isOver variable and set it once,
				// so that the move-in stuff gets fired only once.
				if ( !sortable.isOver ) {
					sortable.isOver = 1;

					// Store draggable's parent in case we need to reappend to it later.
					draggable._parent = ui.helper.parent();

					sortable.currentItem = ui.helper
						.appendTo( sortable.element )
						.data( "ui-sortable-item", true );

					// Store helper option to later restore it
					sortable.options._helper = sortable.options.helper;

					sortable.options.helper = function() {
						return ui.helper[ 0 ];
					};

					// Fire the start events of the sortable with our passed browser event,
					// and our own helper (so it doesn't create a new one)
					event.target = sortable.currentItem[ 0 ];
					sortable._mouseCapture( event, true );
					sortable._mouseStart( event, true, true );

					// Because the browser event is way off the new appended portlet,
					// modify necessary variables to reflect the changes
					sortable.offset.click.top = draggable.offset.click.top;
					sortable.offset.click.left = draggable.offset.click.left;
					sortable.offset.parent.left -= draggable.offset.parent.left -
						sortable.offset.parent.left;
					sortable.offset.parent.top -= draggable.offset.parent.top -
						sortable.offset.parent.top;

					draggable._trigger( "toSortable", event );

					// Inform draggable that the helper is in a valid drop zone,
					// used solely in the revert option to handle "valid/invalid".
					draggable.dropped = sortable.element;

					// Need to refreshPositions of all sortables in the case that
					// adding to one sortable changes the location of the other sortables (#9675)
					$.each( draggable.sortables, function() {
						this.refreshPositions();
					} );

					// Hack so receive/update callbacks work (mostly)
					draggable.currentItem = draggable.element;
					sortable.fromOutside = draggable;
				}

				if ( sortable.currentItem ) {
					sortable._mouseDrag( event );

					// Copy the sortable's position because the draggable's can potentially reflect
					// a relative position, while sortable is always absolute, which the dragged
					// element has now become. (#8809)
					ui.position = sortable.position;
				}
			} else {

				// If it doesn't intersect with the sortable, and it intersected before,
				// we fake the drag stop of the sortable, but make sure it doesn't remove
				// the helper by using cancelHelperRemoval.
				if ( sortable.isOver ) {

					sortable.isOver = 0;
					sortable.cancelHelperRemoval = true;

					// Calling sortable's mouseStop would trigger a revert,
					// so revert must be temporarily false until after mouseStop is called.
					sortable.options._revert = sortable.options.revert;
					sortable.options.revert = false;

					sortable._trigger( "out", event, sortable._uiHash( sortable ) );
					sortable._mouseStop( event, true );

					// Restore sortable behaviors that were modfied
					// when the draggable entered the sortable area (#9481)
					sortable.options.revert = sortable.options._revert;
					sortable.options.helper = sortable.options._helper;

					if ( sortable.placeholder ) {
						sortable.placeholder.remove();
					}

					// Restore and recalculate the draggable's offset considering the sortable
					// may have modified them in unexpected ways. (#8809, #10669)
					ui.helper.appendTo( draggable._parent );
					draggable._refreshOffsets( event );
					ui.position = draggable._generatePosition( event, true );

					draggable._trigger( "fromSortable", event );

					// Inform draggable that the helper is no longer in a valid drop zone
					draggable.dropped = false;

					// Need to refreshPositions of all sortables just in case removing
					// from one sortable changes the location of other sortables (#9675)
					$.each( draggable.sortables, function() {
						this.refreshPositions();
					} );
				}
			}
		} );
	}
} );

$.ui.plugin.add( "draggable", "cursor", {
	start: function( event, ui, instance ) {
		var t = $( "body" ),
			o = instance.options;

		if ( t.css( "cursor" ) ) {
			o._cursor = t.css( "cursor" );
		}
		t.css( "cursor", o.cursor );
	},
	stop: function( event, ui, instance ) {
		var o = instance.options;
		if ( o._cursor ) {
			$( "body" ).css( "cursor", o._cursor );
		}
	}
} );

$.ui.plugin.add( "draggable", "opacity", {
	start: function( event, ui, instance ) {
		var t = $( ui.helper ),
			o = instance.options;
		if ( t.css( "opacity" ) ) {
			o._opacity = t.css( "opacity" );
		}
		t.css( "opacity", o.opacity );
	},
	stop: function( event, ui, instance ) {
		var o = instance.options;
		if ( o._opacity ) {
			$( ui.helper ).css( "opacity", o._opacity );
		}
	}
} );

$.ui.plugin.add( "draggable", "scroll", {
	start: function( event, ui, i ) {
		if ( !i.scrollParentNotHidden ) {
			i.scrollParentNotHidden = i.helper.scrollParent( false );
		}

		if ( i.scrollParentNotHidden[ 0 ] !== i.document[ 0 ] &&
				i.scrollParentNotHidden[ 0 ].tagName !== "HTML" ) {
			i.overflowOffset = i.scrollParentNotHidden.offset();
		}
	},
	drag: function( event, ui, i  ) {

		var o = i.options,
			scrolled = false,
			scrollParent = i.scrollParentNotHidden[ 0 ],
			document = i.document[ 0 ];

		if ( scrollParent !== document && scrollParent.tagName !== "HTML" ) {
			if ( !o.axis || o.axis !== "x" ) {
				if ( ( i.overflowOffset.top + scrollParent.offsetHeight ) - event.pageY <
						o.scrollSensitivity ) {
					scrollParent.scrollTop = scrolled = scrollParent.scrollTop + o.scrollSpeed;
				} else if ( event.pageY - i.overflowOffset.top < o.scrollSensitivity ) {
					scrollParent.scrollTop = scrolled = scrollParent.scrollTop - o.scrollSpeed;
				}
			}

			if ( !o.axis || o.axis !== "y" ) {
				if ( ( i.overflowOffset.left + scrollParent.offsetWidth ) - event.pageX <
						o.scrollSensitivity ) {
					scrollParent.scrollLeft = scrolled = scrollParent.scrollLeft + o.scrollSpeed;
				} else if ( event.pageX - i.overflowOffset.left < o.scrollSensitivity ) {
					scrollParent.scrollLeft = scrolled = scrollParent.scrollLeft - o.scrollSpeed;
				}
			}

		} else {

			if ( !o.axis || o.axis !== "x" ) {
				if ( event.pageY - $( document ).scrollTop() < o.scrollSensitivity ) {
					scrolled = $( document ).scrollTop( $( document ).scrollTop() - o.scrollSpeed );
				} else if ( $( window ).height() - ( event.pageY - $( document ).scrollTop() ) <
						o.scrollSensitivity ) {
					scrolled = $( document ).scrollTop( $( document ).scrollTop() + o.scrollSpeed );
				}
			}

			if ( !o.axis || o.axis !== "y" ) {
				if ( event.pageX - $( document ).scrollLeft() < o.scrollSensitivity ) {
					scrolled = $( document ).scrollLeft(
						$( document ).scrollLeft() - o.scrollSpeed
					);
				} else if ( $( window ).width() - ( event.pageX - $( document ).scrollLeft() ) <
						o.scrollSensitivity ) {
					scrolled = $( document ).scrollLeft(
						$( document ).scrollLeft() + o.scrollSpeed
					);
				}
			}

		}

		if ( scrolled !== false && $.ui.ddmanager && !o.dropBehaviour ) {
			$.ui.ddmanager.prepareOffsets( i, event );
		}

	}
} );

$.ui.plugin.add( "draggable", "snap", {
	start: function( event, ui, i ) {

		var o = i.options;

		i.snapElements = [];

		$( o.snap.constructor !== String ? ( o.snap.items || ":data(ui-draggable)" ) : o.snap )
			.each( function() {
				var $t = $( this ),
					$o = $t.offset();
				if ( this !== i.element[ 0 ] ) {
					i.snapElements.push( {
						item: this,
						width: $t.outerWidth(), height: $t.outerHeight(),
						top: $o.top, left: $o.left
					} );
				}
			} );

	},
	drag: function( event, ui, inst ) {

		var ts, bs, ls, rs, l, r, t, b, i, first,
			o = inst.options,
			d = o.snapTolerance,
			x1 = ui.offset.left, x2 = x1 + inst.helperProportions.width,
			y1 = ui.offset.top, y2 = y1 + inst.helperProportions.height;

		for ( i = inst.snapElements.length - 1; i >= 0; i-- ) {

			l = inst.snapElements[ i ].left - inst.margins.left;
			r = l + inst.snapElements[ i ].width;
			t = inst.snapElements[ i ].top - inst.margins.top;
			b = t + inst.snapElements[ i ].height;

			if ( x2 < l - d || x1 > r + d || y2 < t - d || y1 > b + d ||
					!$.contains( inst.snapElements[ i ].item.ownerDocument,
					inst.snapElements[ i ].item ) ) {
				if ( inst.snapElements[ i ].snapping ) {
					( inst.options.snap.release &&
						inst.options.snap.release.call(
							inst.element,
							event,
							$.extend( inst._uiHash(), { snapItem: inst.snapElements[ i ].item } )
						) );
				}
				inst.snapElements[ i ].snapping = false;
				continue;
			}

			if ( o.snapMode !== "inner" ) {
				ts = Math.abs( t - y2 ) <= d;
				bs = Math.abs( b - y1 ) <= d;
				ls = Math.abs( l - x2 ) <= d;
				rs = Math.abs( r - x1 ) <= d;
				if ( ts ) {
					ui.position.top = inst._convertPositionTo( "relative", {
						top: t - inst.helperProportions.height,
						left: 0
					} ).top;
				}
				if ( bs ) {
					ui.position.top = inst._convertPositionTo( "relative", {
						top: b,
						left: 0
					} ).top;
				}
				if ( ls ) {
					ui.position.left = inst._convertPositionTo( "relative", {
						top: 0,
						left: l - inst.helperProportions.width
					} ).left;
				}
				if ( rs ) {
					ui.position.left = inst._convertPositionTo( "relative", {
						top: 0,
						left: r
					} ).left;
				}
			}

			first = ( ts || bs || ls || rs );

			if ( o.snapMode !== "outer" ) {
				ts = Math.abs( t - y1 ) <= d;
				bs = Math.abs( b - y2 ) <= d;
				ls = Math.abs( l - x1 ) <= d;
				rs = Math.abs( r - x2 ) <= d;
				if ( ts ) {
					ui.position.top = inst._convertPositionTo( "relative", {
						top: t,
						left: 0
					} ).top;
				}
				if ( bs ) {
					ui.position.top = inst._convertPositionTo( "relative", {
						top: b - inst.helperProportions.height,
						left: 0
					} ).top;
				}
				if ( ls ) {
					ui.position.left = inst._convertPositionTo( "relative", {
						top: 0,
						left: l
					} ).left;
				}
				if ( rs ) {
					ui.position.left = inst._convertPositionTo( "relative", {
						top: 0,
						left: r - inst.helperProportions.width
					} ).left;
				}
			}

			if ( !inst.snapElements[ i ].snapping && ( ts || bs || ls || rs || first ) ) {
				( inst.options.snap.snap &&
					inst.options.snap.snap.call(
						inst.element,
						event,
						$.extend( inst._uiHash(), {
							snapItem: inst.snapElements[ i ].item
						} ) ) );
			}
			inst.snapElements[ i ].snapping = ( ts || bs || ls || rs || first );

		}

	}
} );

$.ui.plugin.add( "draggable", "stack", {
	start: function( event, ui, instance ) {
		var min,
			o = instance.options,
			group = $.makeArray( $( o.stack ) ).sort( function( a, b ) {
				return ( parseInt( $( a ).css( "zIndex" ), 10 ) || 0 ) -
					( parseInt( $( b ).css( "zIndex" ), 10 ) || 0 );
			} );

		if ( !group.length ) { return; }

		min = parseInt( $( group[ 0 ] ).css( "zIndex" ), 10 ) || 0;
		$( group ).each( function( i ) {
			$( this ).css( "zIndex", min + i );
		} );
		this.css( "zIndex", ( min + group.length ) );
	}
} );

$.ui.plugin.add( "draggable", "zIndex", {
	start: function( event, ui, instance ) {
		var t = $( ui.helper ),
			o = instance.options;

		if ( t.css( "zIndex" ) ) {
			o._zIndex = t.css( "zIndex" );
		}
		t.css( "zIndex", o.zIndex );
	},
	stop: function( event, ui, instance ) {
		var o = instance.options;

		if ( o._zIndex ) {
			$( ui.helper ).css( "zIndex", o._zIndex );
		}
	}
} );

var widgetsDraggable = $.ui.draggable;


/*!
 * jQuery UI Resizable 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Resizable
//>>group: Interactions
//>>description: Enables resize functionality for any element.
//>>docs: http://api.jqueryui.com/resizable/
//>>demos: http://jqueryui.com/resizable/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/resizable.css
//>>css.theme: ../../themes/base/theme.css



$.widget( "ui.resizable", $.ui.mouse, {
	version: "1.12.1",
	widgetEventPrefix: "resize",
	options: {
		alsoResize: false,
		animate: false,
		animateDuration: "slow",
		animateEasing: "swing",
		aspectRatio: false,
		autoHide: false,
		classes: {
			"ui-resizable-se": "ui-icon ui-icon-gripsmall-diagonal-se"
		},
		containment: false,
		ghost: false,
		grid: false,
		handles: "e,s,se",
		helper: false,
		maxHeight: null,
		maxWidth: null,
		minHeight: 10,
		minWidth: 10,

		// See #7960
		zIndex: 90,

		// Callbacks
		resize: null,
		start: null,
		stop: null
	},

	_num: function( value ) {
		return parseFloat( value ) || 0;
	},

	_isNumber: function( value ) {
		return !isNaN( parseFloat( value ) );
	},

	_hasScroll: function( el, a ) {

		if ( $( el ).css( "overflow" ) === "hidden" ) {
			return false;
		}

		var scroll = ( a && a === "left" ) ? "scrollLeft" : "scrollTop",
			has = false;

		if ( el[ scroll ] > 0 ) {
			return true;
		}

		// TODO: determine which cases actually cause this to happen
		// if the element doesn't have the scroll set, see if it's possible to
		// set the scroll
		el[ scroll ] = 1;
		has = ( el[ scroll ] > 0 );
		el[ scroll ] = 0;
		return has;
	},

	_create: function() {

		var margins,
			o = this.options,
			that = this;
		this._addClass( "ui-resizable" );

		$.extend( this, {
			_aspectRatio: !!( o.aspectRatio ),
			aspectRatio: o.aspectRatio,
			originalElement: this.element,
			_proportionallyResizeElements: [],
			_helper: o.helper || o.ghost || o.animate ? o.helper || "ui-resizable-helper" : null
		} );

		// Wrap the element if it cannot hold child nodes
		if ( this.element[ 0 ].nodeName.match( /^(canvas|textarea|input|select|button|img)$/i ) ) {

			this.element.wrap(
				$( "<div class='ui-wrapper' style='overflow: hidden;'></div>" ).css( {
					position: this.element.css( "position" ),
					width: this.element.outerWidth(),
					height: this.element.outerHeight(),
					top: this.element.css( "top" ),
					left: this.element.css( "left" )
				} )
			);

			this.element = this.element.parent().data(
				"ui-resizable", this.element.resizable( "instance" )
			);

			this.elementIsWrapper = true;

			margins = {
				marginTop: this.originalElement.css( "marginTop" ),
				marginRight: this.originalElement.css( "marginRight" ),
				marginBottom: this.originalElement.css( "marginBottom" ),
				marginLeft: this.originalElement.css( "marginLeft" )
			};

			this.element.css( margins );
			this.originalElement.css( "margin", 0 );

			// support: Safari
			// Prevent Safari textarea resize
			this.originalResizeStyle = this.originalElement.css( "resize" );
			this.originalElement.css( "resize", "none" );

			this._proportionallyResizeElements.push( this.originalElement.css( {
				position: "static",
				zoom: 1,
				display: "block"
			} ) );

			// Support: IE9
			// avoid IE jump (hard set the margin)
			this.originalElement.css( margins );

			this._proportionallyResize();
		}

		this._setupHandles();

		if ( o.autoHide ) {
			$( this.element )
				.on( "mouseenter", function() {
					if ( o.disabled ) {
						return;
					}
					that._removeClass( "ui-resizable-autohide" );
					that._handles.show();
				} )
				.on( "mouseleave", function() {
					if ( o.disabled ) {
						return;
					}
					if ( !that.resizing ) {
						that._addClass( "ui-resizable-autohide" );
						that._handles.hide();
					}
				} );
		}

		this._mouseInit();
	},

	_destroy: function() {

		this._mouseDestroy();

		var wrapper,
			_destroy = function( exp ) {
				$( exp )
					.removeData( "resizable" )
					.removeData( "ui-resizable" )
					.off( ".resizable" )
					.find( ".ui-resizable-handle" )
						.remove();
			};

		// TODO: Unwrap at same DOM position
		if ( this.elementIsWrapper ) {
			_destroy( this.element );
			wrapper = this.element;
			this.originalElement.css( {
				position: wrapper.css( "position" ),
				width: wrapper.outerWidth(),
				height: wrapper.outerHeight(),
				top: wrapper.css( "top" ),
				left: wrapper.css( "left" )
			} ).insertAfter( wrapper );
			wrapper.remove();
		}

		this.originalElement.css( "resize", this.originalResizeStyle );
		_destroy( this.originalElement );

		return this;
	},

	_setOption: function( key, value ) {
		this._super( key, value );

		switch ( key ) {
		case "handles":
			this._removeHandles();
			this._setupHandles();
			break;
		default:
			break;
		}
	},

	_setupHandles: function() {
		var o = this.options, handle, i, n, hname, axis, that = this;
		this.handles = o.handles ||
			( !$( ".ui-resizable-handle", this.element ).length ?
				"e,s,se" : {
					n: ".ui-resizable-n",
					e: ".ui-resizable-e",
					s: ".ui-resizable-s",
					w: ".ui-resizable-w",
					se: ".ui-resizable-se",
					sw: ".ui-resizable-sw",
					ne: ".ui-resizable-ne",
					nw: ".ui-resizable-nw"
				} );

		this._handles = $();
		if ( this.handles.constructor === String ) {

			if ( this.handles === "all" ) {
				this.handles = "n,e,s,w,se,sw,ne,nw";
			}

			n = this.handles.split( "," );
			this.handles = {};

			for ( i = 0; i < n.length; i++ ) {

				handle = $.trim( n[ i ] );
				hname = "ui-resizable-" + handle;
				axis = $( "<div>" );
				this._addClass( axis, "ui-resizable-handle " + hname );

				axis.css( { zIndex: o.zIndex } );

				this.handles[ handle ] = ".ui-resizable-" + handle;
				this.element.append( axis );
			}

		}

		this._renderAxis = function( target ) {

			var i, axis, padPos, padWrapper;

			target = target || this.element;

			for ( i in this.handles ) {

				if ( this.handles[ i ].constructor === String ) {
					this.handles[ i ] = this.element.children( this.handles[ i ] ).first().show();
				} else if ( this.handles[ i ].jquery || this.handles[ i ].nodeType ) {
					this.handles[ i ] = $( this.handles[ i ] );
					this._on( this.handles[ i ], { "mousedown": that._mouseDown } );
				}

				if ( this.elementIsWrapper &&
						this.originalElement[ 0 ]
							.nodeName
							.match( /^(textarea|input|select|button)$/i ) ) {
					axis = $( this.handles[ i ], this.element );

					padWrapper = /sw|ne|nw|se|n|s/.test( i ) ?
						axis.outerHeight() :
						axis.outerWidth();

					padPos = [ "padding",
						/ne|nw|n/.test( i ) ? "Top" :
						/se|sw|s/.test( i ) ? "Bottom" :
						/^e$/.test( i ) ? "Right" : "Left" ].join( "" );

					target.css( padPos, padWrapper );

					this._proportionallyResize();
				}

				this._handles = this._handles.add( this.handles[ i ] );
			}
		};

		// TODO: make renderAxis a prototype function
		this._renderAxis( this.element );

		this._handles = this._handles.add( this.element.find( ".ui-resizable-handle" ) );
		this._handles.disableSelection();

		this._handles.on( "mouseover", function() {
			if ( !that.resizing ) {
				if ( this.className ) {
					axis = this.className.match( /ui-resizable-(se|sw|ne|nw|n|e|s|w)/i );
				}
				that.axis = axis && axis[ 1 ] ? axis[ 1 ] : "se";
			}
		} );

		if ( o.autoHide ) {
			this._handles.hide();
			this._addClass( "ui-resizable-autohide" );
		}
	},

	_removeHandles: function() {
		this._handles.remove();
	},

	_mouseCapture: function( event ) {
		var i, handle,
			capture = false;

		for ( i in this.handles ) {
			handle = $( this.handles[ i ] )[ 0 ];
			if ( handle === event.target || $.contains( handle, event.target ) ) {
				capture = true;
			}
		}

		return !this.options.disabled && capture;
	},

	_mouseStart: function( event ) {

		var curleft, curtop, cursor,
			o = this.options,
			el = this.element;

		this.resizing = true;

		this._renderProxy();

		curleft = this._num( this.helper.css( "left" ) );
		curtop = this._num( this.helper.css( "top" ) );

		if ( o.containment ) {
			curleft += $( o.containment ).scrollLeft() || 0;
			curtop += $( o.containment ).scrollTop() || 0;
		}

		this.offset = this.helper.offset();
		this.position = { left: curleft, top: curtop };

		this.size = this._helper ? {
				width: this.helper.width(),
				height: this.helper.height()
			} : {
				width: el.width(),
				height: el.height()
			};

		this.originalSize = this._helper ? {
				width: el.outerWidth(),
				height: el.outerHeight()
			} : {
				width: el.width(),
				height: el.height()
			};

		this.sizeDiff = {
			width: el.outerWidth() - el.width(),
			height: el.outerHeight() - el.height()
		};

		this.originalPosition = { left: curleft, top: curtop };
		this.originalMousePosition = { left: event.pageX, top: event.pageY };

		this.aspectRatio = ( typeof o.aspectRatio === "number" ) ?
			o.aspectRatio :
			( ( this.originalSize.width / this.originalSize.height ) || 1 );

		cursor = $( ".ui-resizable-" + this.axis ).css( "cursor" );
		$( "body" ).css( "cursor", cursor === "auto" ? this.axis + "-resize" : cursor );

		this._addClass( "ui-resizable-resizing" );
		this._propagate( "start", event );
		return true;
	},

	_mouseDrag: function( event ) {

		var data, props,
			smp = this.originalMousePosition,
			a = this.axis,
			dx = ( event.pageX - smp.left ) || 0,
			dy = ( event.pageY - smp.top ) || 0,
			trigger = this._change[ a ];

		this._updatePrevProperties();

		if ( !trigger ) {
			return false;
		}

		data = trigger.apply( this, [ event, dx, dy ] );

		this._updateVirtualBoundaries( event.shiftKey );
		if ( this._aspectRatio || event.shiftKey ) {
			data = this._updateRatio( data, event );
		}

		data = this._respectSize( data, event );

		this._updateCache( data );

		this._propagate( "resize", event );

		props = this._applyChanges();

		if ( !this._helper && this._proportionallyResizeElements.length ) {
			this._proportionallyResize();
		}

		if ( !$.isEmptyObject( props ) ) {
			this._updatePrevProperties();
			this._trigger( "resize", event, this.ui() );
			this._applyChanges();
		}

		return false;
	},

	_mouseStop: function( event ) {

		this.resizing = false;
		var pr, ista, soffseth, soffsetw, s, left, top,
			o = this.options, that = this;

		if ( this._helper ) {

			pr = this._proportionallyResizeElements;
			ista = pr.length && ( /textarea/i ).test( pr[ 0 ].nodeName );
			soffseth = ista && this._hasScroll( pr[ 0 ], "left" ) ? 0 : that.sizeDiff.height;
			soffsetw = ista ? 0 : that.sizeDiff.width;

			s = {
				width: ( that.helper.width()  - soffsetw ),
				height: ( that.helper.height() - soffseth )
			};
			left = ( parseFloat( that.element.css( "left" ) ) +
				( that.position.left - that.originalPosition.left ) ) || null;
			top = ( parseFloat( that.element.css( "top" ) ) +
				( that.position.top - that.originalPosition.top ) ) || null;

			if ( !o.animate ) {
				this.element.css( $.extend( s, { top: top, left: left } ) );
			}

			that.helper.height( that.size.height );
			that.helper.width( that.size.width );

			if ( this._helper && !o.animate ) {
				this._proportionallyResize();
			}
		}

		$( "body" ).css( "cursor", "auto" );

		this._removeClass( "ui-resizable-resizing" );

		this._propagate( "stop", event );

		if ( this._helper ) {
			this.helper.remove();
		}

		return false;

	},

	_updatePrevProperties: function() {
		this.prevPosition = {
			top: this.position.top,
			left: this.position.left
		};
		this.prevSize = {
			width: this.size.width,
			height: this.size.height
		};
	},

	_applyChanges: function() {
		var props = {};

		if ( this.position.top !== this.prevPosition.top ) {
			props.top = this.position.top + "px";
		}
		if ( this.position.left !== this.prevPosition.left ) {
			props.left = this.position.left + "px";
		}
		if ( this.size.width !== this.prevSize.width ) {
			props.width = this.size.width + "px";
		}
		if ( this.size.height !== this.prevSize.height ) {
			props.height = this.size.height + "px";
		}

		this.helper.css( props );

		return props;
	},

	_updateVirtualBoundaries: function( forceAspectRatio ) {
		var pMinWidth, pMaxWidth, pMinHeight, pMaxHeight, b,
			o = this.options;

		b = {
			minWidth: this._isNumber( o.minWidth ) ? o.minWidth : 0,
			maxWidth: this._isNumber( o.maxWidth ) ? o.maxWidth : Infinity,
			minHeight: this._isNumber( o.minHeight ) ? o.minHeight : 0,
			maxHeight: this._isNumber( o.maxHeight ) ? o.maxHeight : Infinity
		};

		if ( this._aspectRatio || forceAspectRatio ) {
			pMinWidth = b.minHeight * this.aspectRatio;
			pMinHeight = b.minWidth / this.aspectRatio;
			pMaxWidth = b.maxHeight * this.aspectRatio;
			pMaxHeight = b.maxWidth / this.aspectRatio;

			if ( pMinWidth > b.minWidth ) {
				b.minWidth = pMinWidth;
			}
			if ( pMinHeight > b.minHeight ) {
				b.minHeight = pMinHeight;
			}
			if ( pMaxWidth < b.maxWidth ) {
				b.maxWidth = pMaxWidth;
			}
			if ( pMaxHeight < b.maxHeight ) {
				b.maxHeight = pMaxHeight;
			}
		}
		this._vBoundaries = b;
	},

	_updateCache: function( data ) {
		this.offset = this.helper.offset();
		if ( this._isNumber( data.left ) ) {
			this.position.left = data.left;
		}
		if ( this._isNumber( data.top ) ) {
			this.position.top = data.top;
		}
		if ( this._isNumber( data.height ) ) {
			this.size.height = data.height;
		}
		if ( this._isNumber( data.width ) ) {
			this.size.width = data.width;
		}
	},

	_updateRatio: function( data ) {

		var cpos = this.position,
			csize = this.size,
			a = this.axis;

		if ( this._isNumber( data.height ) ) {
			data.width = ( data.height * this.aspectRatio );
		} else if ( this._isNumber( data.width ) ) {
			data.height = ( data.width / this.aspectRatio );
		}

		if ( a === "sw" ) {
			data.left = cpos.left + ( csize.width - data.width );
			data.top = null;
		}
		if ( a === "nw" ) {
			data.top = cpos.top + ( csize.height - data.height );
			data.left = cpos.left + ( csize.width - data.width );
		}

		return data;
	},

	_respectSize: function( data ) {

		var o = this._vBoundaries,
			a = this.axis,
			ismaxw = this._isNumber( data.width ) && o.maxWidth && ( o.maxWidth < data.width ),
			ismaxh = this._isNumber( data.height ) && o.maxHeight && ( o.maxHeight < data.height ),
			isminw = this._isNumber( data.width ) && o.minWidth && ( o.minWidth > data.width ),
			isminh = this._isNumber( data.height ) && o.minHeight && ( o.minHeight > data.height ),
			dw = this.originalPosition.left + this.originalSize.width,
			dh = this.originalPosition.top + this.originalSize.height,
			cw = /sw|nw|w/.test( a ), ch = /nw|ne|n/.test( a );
		if ( isminw ) {
			data.width = o.minWidth;
		}
		if ( isminh ) {
			data.height = o.minHeight;
		}
		if ( ismaxw ) {
			data.width = o.maxWidth;
		}
		if ( ismaxh ) {
			data.height = o.maxHeight;
		}

		if ( isminw && cw ) {
			data.left = dw - o.minWidth;
		}
		if ( ismaxw && cw ) {
			data.left = dw - o.maxWidth;
		}
		if ( isminh && ch ) {
			data.top = dh - o.minHeight;
		}
		if ( ismaxh && ch ) {
			data.top = dh - o.maxHeight;
		}

		// Fixing jump error on top/left - bug #2330
		if ( !data.width && !data.height && !data.left && data.top ) {
			data.top = null;
		} else if ( !data.width && !data.height && !data.top && data.left ) {
			data.left = null;
		}

		return data;
	},

	_getPaddingPlusBorderDimensions: function( element ) {
		var i = 0,
			widths = [],
			borders = [
				element.css( "borderTopWidth" ),
				element.css( "borderRightWidth" ),
				element.css( "borderBottomWidth" ),
				element.css( "borderLeftWidth" )
			],
			paddings = [
				element.css( "paddingTop" ),
				element.css( "paddingRight" ),
				element.css( "paddingBottom" ),
				element.css( "paddingLeft" )
			];

		for ( ; i < 4; i++ ) {
			widths[ i ] = ( parseFloat( borders[ i ] ) || 0 );
			widths[ i ] += ( parseFloat( paddings[ i ] ) || 0 );
		}

		return {
			height: widths[ 0 ] + widths[ 2 ],
			width: widths[ 1 ] + widths[ 3 ]
		};
	},

	_proportionallyResize: function() {

		if ( !this._proportionallyResizeElements.length ) {
			return;
		}

		var prel,
			i = 0,
			element = this.helper || this.element;

		for ( ; i < this._proportionallyResizeElements.length; i++ ) {

			prel = this._proportionallyResizeElements[ i ];

			// TODO: Seems like a bug to cache this.outerDimensions
			// considering that we are in a loop.
			if ( !this.outerDimensions ) {
				this.outerDimensions = this._getPaddingPlusBorderDimensions( prel );
			}

			prel.css( {
				height: ( element.height() - this.outerDimensions.height ) || 0,
				width: ( element.width() - this.outerDimensions.width ) || 0
			} );

		}

	},

	_renderProxy: function() {

		var el = this.element, o = this.options;
		this.elementOffset = el.offset();

		if ( this._helper ) {

			this.helper = this.helper || $( "<div style='overflow:hidden;'></div>" );

			this._addClass( this.helper, this._helper );
			this.helper.css( {
				width: this.element.outerWidth(),
				height: this.element.outerHeight(),
				position: "absolute",
				left: this.elementOffset.left + "px",
				top: this.elementOffset.top + "px",
				zIndex: ++o.zIndex //TODO: Don't modify option
			} );

			this.helper
				.appendTo( "body" )
				.disableSelection();

		} else {
			this.helper = this.element;
		}

	},

	_change: {
		e: function( event, dx ) {
			return { width: this.originalSize.width + dx };
		},
		w: function( event, dx ) {
			var cs = this.originalSize, sp = this.originalPosition;
			return { left: sp.left + dx, width: cs.width - dx };
		},
		n: function( event, dx, dy ) {
			var cs = this.originalSize, sp = this.originalPosition;
			return { top: sp.top + dy, height: cs.height - dy };
		},
		s: function( event, dx, dy ) {
			return { height: this.originalSize.height + dy };
		},
		se: function( event, dx, dy ) {
			return $.extend( this._change.s.apply( this, arguments ),
				this._change.e.apply( this, [ event, dx, dy ] ) );
		},
		sw: function( event, dx, dy ) {
			return $.extend( this._change.s.apply( this, arguments ),
				this._change.w.apply( this, [ event, dx, dy ] ) );
		},
		ne: function( event, dx, dy ) {
			return $.extend( this._change.n.apply( this, arguments ),
				this._change.e.apply( this, [ event, dx, dy ] ) );
		},
		nw: function( event, dx, dy ) {
			return $.extend( this._change.n.apply( this, arguments ),
				this._change.w.apply( this, [ event, dx, dy ] ) );
		}
	},

	_propagate: function( n, event ) {
		$.ui.plugin.call( this, n, [ event, this.ui() ] );
		( n !== "resize" && this._trigger( n, event, this.ui() ) );
	},

	plugins: {},

	ui: function() {
		return {
			originalElement: this.originalElement,
			element: this.element,
			helper: this.helper,
			position: this.position,
			size: this.size,
			originalSize: this.originalSize,
			originalPosition: this.originalPosition
		};
	}

} );

/*
 * Resizable Extensions
 */

$.ui.plugin.add( "resizable", "animate", {

	stop: function( event ) {
		var that = $( this ).resizable( "instance" ),
			o = that.options,
			pr = that._proportionallyResizeElements,
			ista = pr.length && ( /textarea/i ).test( pr[ 0 ].nodeName ),
			soffseth = ista && that._hasScroll( pr[ 0 ], "left" ) ? 0 : that.sizeDiff.height,
			soffsetw = ista ? 0 : that.sizeDiff.width,
			style = {
				width: ( that.size.width - soffsetw ),
				height: ( that.size.height - soffseth )
			},
			left = ( parseFloat( that.element.css( "left" ) ) +
				( that.position.left - that.originalPosition.left ) ) || null,
			top = ( parseFloat( that.element.css( "top" ) ) +
				( that.position.top - that.originalPosition.top ) ) || null;

		that.element.animate(
			$.extend( style, top && left ? { top: top, left: left } : {} ), {
				duration: o.animateDuration,
				easing: o.animateEasing,
				step: function() {

					var data = {
						width: parseFloat( that.element.css( "width" ) ),
						height: parseFloat( that.element.css( "height" ) ),
						top: parseFloat( that.element.css( "top" ) ),
						left: parseFloat( that.element.css( "left" ) )
					};

					if ( pr && pr.length ) {
						$( pr[ 0 ] ).css( { width: data.width, height: data.height } );
					}

					// Propagating resize, and updating values for each animation step
					that._updateCache( data );
					that._propagate( "resize", event );

				}
			}
		);
	}

} );

$.ui.plugin.add( "resizable", "containment", {

	start: function() {
		var element, p, co, ch, cw, width, height,
			that = $( this ).resizable( "instance" ),
			o = that.options,
			el = that.element,
			oc = o.containment,
			ce = ( oc instanceof $ ) ?
				oc.get( 0 ) :
				( /parent/.test( oc ) ) ? el.parent().get( 0 ) : oc;

		if ( !ce ) {
			return;
		}

		that.containerElement = $( ce );

		if ( /document/.test( oc ) || oc === document ) {
			that.containerOffset = {
				left: 0,
				top: 0
			};
			that.containerPosition = {
				left: 0,
				top: 0
			};

			that.parentData = {
				element: $( document ),
				left: 0,
				top: 0,
				width: $( document ).width(),
				height: $( document ).height() || document.body.parentNode.scrollHeight
			};
		} else {
			element = $( ce );
			p = [];
			$( [ "Top", "Right", "Left", "Bottom" ] ).each( function( i, name ) {
				p[ i ] = that._num( element.css( "padding" + name ) );
			} );

			that.containerOffset = element.offset();
			that.containerPosition = element.position();
			that.containerSize = {
				height: ( element.innerHeight() - p[ 3 ] ),
				width: ( element.innerWidth() - p[ 1 ] )
			};

			co = that.containerOffset;
			ch = that.containerSize.height;
			cw = that.containerSize.width;
			width = ( that._hasScroll ( ce, "left" ) ? ce.scrollWidth : cw );
			height = ( that._hasScroll ( ce ) ? ce.scrollHeight : ch ) ;

			that.parentData = {
				element: ce,
				left: co.left,
				top: co.top,
				width: width,
				height: height
			};
		}
	},

	resize: function( event ) {
		var woset, hoset, isParent, isOffsetRelative,
			that = $( this ).resizable( "instance" ),
			o = that.options,
			co = that.containerOffset,
			cp = that.position,
			pRatio = that._aspectRatio || event.shiftKey,
			cop = {
				top: 0,
				left: 0
			},
			ce = that.containerElement,
			continueResize = true;

		if ( ce[ 0 ] !== document && ( /static/ ).test( ce.css( "position" ) ) ) {
			cop = co;
		}

		if ( cp.left < ( that._helper ? co.left : 0 ) ) {
			that.size.width = that.size.width +
				( that._helper ?
					( that.position.left - co.left ) :
					( that.position.left - cop.left ) );

			if ( pRatio ) {
				that.size.height = that.size.width / that.aspectRatio;
				continueResize = false;
			}
			that.position.left = o.helper ? co.left : 0;
		}

		if ( cp.top < ( that._helper ? co.top : 0 ) ) {
			that.size.height = that.size.height +
				( that._helper ?
					( that.position.top - co.top ) :
					that.position.top );

			if ( pRatio ) {
				that.size.width = that.size.height * that.aspectRatio;
				continueResize = false;
			}
			that.position.top = that._helper ? co.top : 0;
		}

		isParent = that.containerElement.get( 0 ) === that.element.parent().get( 0 );
		isOffsetRelative = /relative|absolute/.test( that.containerElement.css( "position" ) );

		if ( isParent && isOffsetRelative ) {
			that.offset.left = that.parentData.left + that.position.left;
			that.offset.top = that.parentData.top + that.position.top;
		} else {
			that.offset.left = that.element.offset().left;
			that.offset.top = that.element.offset().top;
		}

		woset = Math.abs( that.sizeDiff.width +
			( that._helper ?
				that.offset.left - cop.left :
				( that.offset.left - co.left ) ) );

		hoset = Math.abs( that.sizeDiff.height +
			( that._helper ?
				that.offset.top - cop.top :
				( that.offset.top - co.top ) ) );

		if ( woset + that.size.width >= that.parentData.width ) {
			that.size.width = that.parentData.width - woset;
			if ( pRatio ) {
				that.size.height = that.size.width / that.aspectRatio;
				continueResize = false;
			}
		}

		if ( hoset + that.size.height >= that.parentData.height ) {
			that.size.height = that.parentData.height - hoset;
			if ( pRatio ) {
				that.size.width = that.size.height * that.aspectRatio;
				continueResize = false;
			}
		}

		if ( !continueResize ) {
			that.position.left = that.prevPosition.left;
			that.position.top = that.prevPosition.top;
			that.size.width = that.prevSize.width;
			that.size.height = that.prevSize.height;
		}
	},

	stop: function() {
		var that = $( this ).resizable( "instance" ),
			o = that.options,
			co = that.containerOffset,
			cop = that.containerPosition,
			ce = that.containerElement,
			helper = $( that.helper ),
			ho = helper.offset(),
			w = helper.outerWidth() - that.sizeDiff.width,
			h = helper.outerHeight() - that.sizeDiff.height;

		if ( that._helper && !o.animate && ( /relative/ ).test( ce.css( "position" ) ) ) {
			$( this ).css( {
				left: ho.left - cop.left - co.left,
				width: w,
				height: h
			} );
		}

		if ( that._helper && !o.animate && ( /static/ ).test( ce.css( "position" ) ) ) {
			$( this ).css( {
				left: ho.left - cop.left - co.left,
				width: w,
				height: h
			} );
		}
	}
} );

$.ui.plugin.add( "resizable", "alsoResize", {

	start: function() {
		var that = $( this ).resizable( "instance" ),
			o = that.options;

		$( o.alsoResize ).each( function() {
			var el = $( this );
			el.data( "ui-resizable-alsoresize", {
				width: parseFloat( el.width() ), height: parseFloat( el.height() ),
				left: parseFloat( el.css( "left" ) ), top: parseFloat( el.css( "top" ) )
			} );
		} );
	},

	resize: function( event, ui ) {
		var that = $( this ).resizable( "instance" ),
			o = that.options,
			os = that.originalSize,
			op = that.originalPosition,
			delta = {
				height: ( that.size.height - os.height ) || 0,
				width: ( that.size.width - os.width ) || 0,
				top: ( that.position.top - op.top ) || 0,
				left: ( that.position.left - op.left ) || 0
			};

			$( o.alsoResize ).each( function() {
				var el = $( this ), start = $( this ).data( "ui-resizable-alsoresize" ), style = {},
					css = el.parents( ui.originalElement[ 0 ] ).length ?
							[ "width", "height" ] :
							[ "width", "height", "top", "left" ];

				$.each( css, function( i, prop ) {
					var sum = ( start[ prop ] || 0 ) + ( delta[ prop ] || 0 );
					if ( sum && sum >= 0 ) {
						style[ prop ] = sum || null;
					}
				} );

				el.css( style );
			} );
	},

	stop: function() {
		$( this ).removeData( "ui-resizable-alsoresize" );
	}
} );

$.ui.plugin.add( "resizable", "ghost", {

	start: function() {

		var that = $( this ).resizable( "instance" ), cs = that.size;

		that.ghost = that.originalElement.clone();
		that.ghost.css( {
			opacity: 0.25,
			display: "block",
			position: "relative",
			height: cs.height,
			width: cs.width,
			margin: 0,
			left: 0,
			top: 0
		} );

		that._addClass( that.ghost, "ui-resizable-ghost" );

		// DEPRECATED
		// TODO: remove after 1.12
		if ( $.uiBackCompat !== false && typeof that.options.ghost === "string" ) {

			// Ghost option
			that.ghost.addClass( this.options.ghost );
		}

		that.ghost.appendTo( that.helper );

	},

	resize: function() {
		var that = $( this ).resizable( "instance" );
		if ( that.ghost ) {
			that.ghost.css( {
				position: "relative",
				height: that.size.height,
				width: that.size.width
			} );
		}
	},

	stop: function() {
		var that = $( this ).resizable( "instance" );
		if ( that.ghost && that.helper ) {
			that.helper.get( 0 ).removeChild( that.ghost.get( 0 ) );
		}
	}

} );

$.ui.plugin.add( "resizable", "grid", {

	resize: function() {
		var outerDimensions,
			that = $( this ).resizable( "instance" ),
			o = that.options,
			cs = that.size,
			os = that.originalSize,
			op = that.originalPosition,
			a = that.axis,
			grid = typeof o.grid === "number" ? [ o.grid, o.grid ] : o.grid,
			gridX = ( grid[ 0 ] || 1 ),
			gridY = ( grid[ 1 ] || 1 ),
			ox = Math.round( ( cs.width - os.width ) / gridX ) * gridX,
			oy = Math.round( ( cs.height - os.height ) / gridY ) * gridY,
			newWidth = os.width + ox,
			newHeight = os.height + oy,
			isMaxWidth = o.maxWidth && ( o.maxWidth < newWidth ),
			isMaxHeight = o.maxHeight && ( o.maxHeight < newHeight ),
			isMinWidth = o.minWidth && ( o.minWidth > newWidth ),
			isMinHeight = o.minHeight && ( o.minHeight > newHeight );

		o.grid = grid;

		if ( isMinWidth ) {
			newWidth += gridX;
		}
		if ( isMinHeight ) {
			newHeight += gridY;
		}
		if ( isMaxWidth ) {
			newWidth -= gridX;
		}
		if ( isMaxHeight ) {
			newHeight -= gridY;
		}

		if ( /^(se|s|e)$/.test( a ) ) {
			that.size.width = newWidth;
			that.size.height = newHeight;
		} else if ( /^(ne)$/.test( a ) ) {
			that.size.width = newWidth;
			that.size.height = newHeight;
			that.position.top = op.top - oy;
		} else if ( /^(sw)$/.test( a ) ) {
			that.size.width = newWidth;
			that.size.height = newHeight;
			that.position.left = op.left - ox;
		} else {
			if ( newHeight - gridY <= 0 || newWidth - gridX <= 0 ) {
				outerDimensions = that._getPaddingPlusBorderDimensions( this );
			}

			if ( newHeight - gridY > 0 ) {
				that.size.height = newHeight;
				that.position.top = op.top - oy;
			} else {
				newHeight = gridY - outerDimensions.height;
				that.size.height = newHeight;
				that.position.top = op.top + os.height - newHeight;
			}
			if ( newWidth - gridX > 0 ) {
				that.size.width = newWidth;
				that.position.left = op.left - ox;
			} else {
				newWidth = gridX - outerDimensions.width;
				that.size.width = newWidth;
				that.position.left = op.left + os.width - newWidth;
			}
		}
	}

} );

var widgetsResizable = $.ui.resizable;


/*!
 * jQuery UI Dialog 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Dialog
//>>group: Widgets
//>>description: Displays customizable dialog windows.
//>>docs: http://api.jqueryui.com/dialog/
//>>demos: http://jqueryui.com/dialog/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/dialog.css
//>>css.theme: ../../themes/base/theme.css



$.widget( "ui.dialog", {
	version: "1.12.1",
	options: {
		appendTo: "body",
		autoOpen: true,
		buttons: [],
		classes: {
			"ui-dialog": "ui-corner-all",
			"ui-dialog-titlebar": "ui-corner-all"
		},
		closeOnEscape: true,
		closeText: "Close",
		draggable: true,
		hide: null,
		height: "auto",
		maxHeight: null,
		maxWidth: null,
		minHeight: 150,
		minWidth: 150,
		modal: false,
		position: {
			my: "center",
			at: "center",
			of: window,
			collision: "fit",

			// Ensure the titlebar is always visible
			using: function( pos ) {
				var topOffset = $( this ).css( pos ).offset().top;
				if ( topOffset < 0 ) {
					$( this ).css( "top", pos.top - topOffset );
				}
			}
		},
		resizable: true,
		show: null,
		title: null,
		width: 300,

		// Callbacks
		beforeClose: null,
		close: null,
		drag: null,
		dragStart: null,
		dragStop: null,
		focus: null,
		open: null,
		resize: null,
		resizeStart: null,
		resizeStop: null
	},

	sizeRelatedOptions: {
		buttons: true,
		height: true,
		maxHeight: true,
		maxWidth: true,
		minHeight: true,
		minWidth: true,
		width: true
	},

	resizableRelatedOptions: {
		maxHeight: true,
		maxWidth: true,
		minHeight: true,
		minWidth: true
	},

	_create: function() {
		this.originalCss = {
			display: this.element[ 0 ].style.display,
			width: this.element[ 0 ].style.width,
			minHeight: this.element[ 0 ].style.minHeight,
			maxHeight: this.element[ 0 ].style.maxHeight,
			height: this.element[ 0 ].style.height
		};
		this.originalPosition = {
			parent: this.element.parent(),
			index: this.element.parent().children().index( this.element )
		};
		this.originalTitle = this.element.attr( "title" );
		if ( this.options.title == null && this.originalTitle != null ) {
			this.options.title = this.originalTitle;
		}

		// Dialogs can't be disabled
		if ( this.options.disabled ) {
			this.options.disabled = false;
		}

		this._createWrapper();

		this.element
			.show()
			.removeAttr( "title" )
			.appendTo( this.uiDialog );

		this._addClass( "ui-dialog-content", "ui-widget-content" );

		this._createTitlebar();
		this._createButtonPane();

		if ( this.options.draggable && $.fn.draggable ) {
			this._makeDraggable();
		}
		if ( this.options.resizable && $.fn.resizable ) {
			this._makeResizable();
		}

		this._isOpen = false;

		this._trackFocus();
	},

	_init: function() {
		if ( this.options.autoOpen ) {
			this.open();
		}
	},

	_appendTo: function() {
		var element = this.options.appendTo;
		if ( element && ( element.jquery || element.nodeType ) ) {
			return $( element );
		}
		return this.document.find( element || "body" ).eq( 0 );
	},

	_destroy: function() {
		var next,
			originalPosition = this.originalPosition;

		this._untrackInstance();
		this._destroyOverlay();

		this.element
			.removeUniqueId()
			.css( this.originalCss )

			// Without detaching first, the following becomes really slow
			.detach();

		this.uiDialog.remove();

		if ( this.originalTitle ) {
			this.element.attr( "title", this.originalTitle );
		}

		next = originalPosition.parent.children().eq( originalPosition.index );

		// Don't try to place the dialog next to itself (#8613)
		if ( next.length && next[ 0 ] !== this.element[ 0 ] ) {
			next.before( this.element );
		} else {
			originalPosition.parent.append( this.element );
		}
	},

	widget: function() {
		return this.uiDialog;
	},

	disable: $.noop,
	enable: $.noop,

	close: function( event ) {
		var that = this;

		if ( !this._isOpen || this._trigger( "beforeClose", event ) === false ) {
			return;
		}

		this._isOpen = false;
		this._focusedElement = null;
		this._destroyOverlay();
		this._untrackInstance();

		if ( !this.opener.filter( ":focusable" ).trigger( "focus" ).length ) {

			// Hiding a focused element doesn't trigger blur in WebKit
			// so in case we have nothing to focus on, explicitly blur the active element
			// https://bugs.webkit.org/show_bug.cgi?id=47182
			$.ui.safeBlur( $.ui.safeActiveElement( this.document[ 0 ] ) );
		}

		this._hide( this.uiDialog, this.options.hide, function() {
			that._trigger( "close", event );
		} );
	},

	isOpen: function() {
		return this._isOpen;
	},

	moveToTop: function() {
		this._moveToTop();
	},

	_moveToTop: function( event, silent ) {
		var moved = false,
			zIndices = this.uiDialog.siblings( ".ui-front:visible" ).map( function() {
				return +$( this ).css( "z-index" );
			} ).get(),
			zIndexMax = Math.max.apply( null, zIndices );

		if ( zIndexMax >= +this.uiDialog.css( "z-index" ) ) {
			this.uiDialog.css( "z-index", zIndexMax + 1 );
			moved = true;
		}

		if ( moved && !silent ) {
			this._trigger( "focus", event );
		}
		return moved;
	},

	open: function() {
		var that = this;
		if ( this._isOpen ) {
			if ( this._moveToTop() ) {
				this._focusTabbable();
			}
			return;
		}

		this._isOpen = true;
		this.opener = $( $.ui.safeActiveElement( this.document[ 0 ] ) );

		this._size();
		this._position();
		this._createOverlay();
		this._moveToTop( null, true );

		// Ensure the overlay is moved to the top with the dialog, but only when
		// opening. The overlay shouldn't move after the dialog is open so that
		// modeless dialogs opened after the modal dialog stack properly.
		if ( this.overlay ) {
			this.overlay.css( "z-index", this.uiDialog.css( "z-index" ) - 1 );
		}

		this._show( this.uiDialog, this.options.show, function() {
			that._focusTabbable();
			that._trigger( "focus" );
		} );

		// Track the dialog immediately upon openening in case a focus event
		// somehow occurs outside of the dialog before an element inside the
		// dialog is focused (#10152)
		this._makeFocusTarget();

		this._trigger( "open" );
	},

	_focusTabbable: function() {

		// Set focus to the first match:
		// 1. An element that was focused previously
		// 2. First element inside the dialog matching [autofocus]
		// 3. Tabbable element inside the content element
		// 4. Tabbable element inside the buttonpane
		// 5. The close button
		// 6. The dialog itself
		var hasFocus = this._focusedElement;
		if ( !hasFocus ) {
			hasFocus = this.element.find( "[autofocus]" );
		}
		if ( !hasFocus.length ) {
			hasFocus = this.element.find( ":tabbable" );
		}
		if ( !hasFocus.length ) {
			hasFocus = this.uiDialogButtonPane.find( ":tabbable" );
		}
		if ( !hasFocus.length ) {
			hasFocus = this.uiDialogTitlebarClose.filter( ":tabbable" );
		}
		if ( !hasFocus.length ) {
			hasFocus = this.uiDialog;
		}
		hasFocus.eq( 0 ).trigger( "focus" );
	},

	_keepFocus: function( event ) {
		function checkFocus() {
			var activeElement = $.ui.safeActiveElement( this.document[ 0 ] ),
				isActive = this.uiDialog[ 0 ] === activeElement ||
					$.contains( this.uiDialog[ 0 ], activeElement );
			if ( !isActive ) {
				this._focusTabbable();
			}
		}
		event.preventDefault();
		checkFocus.call( this );

		// support: IE
		// IE <= 8 doesn't prevent moving focus even with event.preventDefault()
		// so we check again later
		this._delay( checkFocus );
	},

	_createWrapper: function() {
		this.uiDialog = $( "<div>" )
			.hide()
			.attr( {

				// Setting tabIndex makes the div focusable
				tabIndex: -1,
				role: "dialog"
			} )
			.appendTo( this._appendTo() );

		this._addClass( this.uiDialog, "ui-dialog", "ui-widget ui-widget-content ui-front" );
		this._on( this.uiDialog, {
			keydown: function( event ) {
				if ( this.options.closeOnEscape && !event.isDefaultPrevented() && event.keyCode &&
						event.keyCode === $.ui.keyCode.ESCAPE ) {
					event.preventDefault();
					this.close( event );
					return;
				}

				// Prevent tabbing out of dialogs
				if ( event.keyCode !== $.ui.keyCode.TAB || event.isDefaultPrevented() ) {
					return;
				}
				var tabbables = this.uiDialog.find( ":tabbable" ),
					first = tabbables.filter( ":first" ),
					last = tabbables.filter( ":last" );

				if ( ( event.target === last[ 0 ] || event.target === this.uiDialog[ 0 ] ) &&
						!event.shiftKey ) {
					this._delay( function() {
						first.trigger( "focus" );
					} );
					event.preventDefault();
				} else if ( ( event.target === first[ 0 ] ||
						event.target === this.uiDialog[ 0 ] ) && event.shiftKey ) {
					this._delay( function() {
						last.trigger( "focus" );
					} );
					event.preventDefault();
				}
			},
			mousedown: function( event ) {
				if ( this._moveToTop( event ) ) {
					this._focusTabbable();
				}
			}
		} );

		// We assume that any existing aria-describedby attribute means
		// that the dialog content is marked up properly
		// otherwise we brute force the content as the description
		if ( !this.element.find( "[aria-describedby]" ).length ) {
			this.uiDialog.attr( {
				"aria-describedby": this.element.uniqueId().attr( "id" )
			} );
		}
	},

	_createTitlebar: function() {
		var uiDialogTitle;

		this.uiDialogTitlebar = $( "<div>" );
		this._addClass( this.uiDialogTitlebar,
			"ui-dialog-titlebar", "ui-widget-header ui-helper-clearfix" );
		this._on( this.uiDialogTitlebar, {
			mousedown: function( event ) {

				// Don't prevent click on close button (#8838)
				// Focusing a dialog that is partially scrolled out of view
				// causes the browser to scroll it into view, preventing the click event
				if ( !$( event.target ).closest( ".ui-dialog-titlebar-close" ) ) {

					// Dialog isn't getting focus when dragging (#8063)
					this.uiDialog.trigger( "focus" );
				}
			}
		} );

		// Support: IE
		// Use type="button" to prevent enter keypresses in textboxes from closing the
		// dialog in IE (#9312)
		this.uiDialogTitlebarClose = $( "<button type='button'></button>" )
			.button( {
				label: $( "<a>" ).text( this.options.closeText ).html(),
				icon: "ui-icon-closethick",
				showLabel: false
			} )
			.appendTo( this.uiDialogTitlebar );

		this._addClass( this.uiDialogTitlebarClose, "ui-dialog-titlebar-close" );
		this._on( this.uiDialogTitlebarClose, {
			click: function( event ) {
				event.preventDefault();
				this.close( event );
			}
		} );

		uiDialogTitle = $( "<span>" ).uniqueId().prependTo( this.uiDialogTitlebar );
		this._addClass( uiDialogTitle, "ui-dialog-title" );
		this._title( uiDialogTitle );

		this.uiDialogTitlebar.prependTo( this.uiDialog );

		this.uiDialog.attr( {
			"aria-labelledby": uiDialogTitle.attr( "id" )
		} );
	},

	_title: function( title ) {
		if ( this.options.title ) {
			title.text( this.options.title );
		} else {
			title.html( "&#160;" );
		}
	},

	_createButtonPane: function() {
		this.uiDialogButtonPane = $( "<div>" );
		this._addClass( this.uiDialogButtonPane, "ui-dialog-buttonpane",
			"ui-widget-content ui-helper-clearfix" );

		this.uiButtonSet = $( "<div>" )
			.appendTo( this.uiDialogButtonPane );
		this._addClass( this.uiButtonSet, "ui-dialog-buttonset" );

		this._createButtons();
	},

	_createButtons: function() {
		var that = this,
			buttons = this.options.buttons;

		// If we already have a button pane, remove it
		this.uiDialogButtonPane.remove();
		this.uiButtonSet.empty();

		if ( $.isEmptyObject( buttons ) || ( $.isArray( buttons ) && !buttons.length ) ) {
			this._removeClass( this.uiDialog, "ui-dialog-buttons" );
			return;
		}

		$.each( buttons, function( name, props ) {
			var click, buttonOptions;
			props = $.isFunction( props ) ?
				{ click: props, text: name } :
				props;

			// Default to a non-submitting button
			props = $.extend( { type: "button" }, props );

			// Change the context for the click callback to be the main element
			click = props.click;
			buttonOptions = {
				icon: props.icon,
				iconPosition: props.iconPosition,
				showLabel: props.showLabel,

				// Deprecated options
				icons: props.icons,
				text: props.text
			};

			delete props.click;
			delete props.icon;
			delete props.iconPosition;
			delete props.showLabel;

			// Deprecated options
			delete props.icons;
			if ( typeof props.text === "boolean" ) {
				delete props.text;
			}

			$( "<button></button>", props )
				.button( buttonOptions )
				.appendTo( that.uiButtonSet )
				.on( "click", function() {
					click.apply( that.element[ 0 ], arguments );
				} );
		} );
		this._addClass( this.uiDialog, "ui-dialog-buttons" );
		this.uiDialogButtonPane.appendTo( this.uiDialog );
	},

	_makeDraggable: function() {
		var that = this,
			options = this.options;

		function filteredUi( ui ) {
			return {
				position: ui.position,
				offset: ui.offset
			};
		}

		this.uiDialog.draggable( {
			cancel: ".ui-dialog-content, .ui-dialog-titlebar-close",
			handle: ".ui-dialog-titlebar",
			containment: "document",
			start: function( event, ui ) {
				that._addClass( $( this ), "ui-dialog-dragging" );
				that._blockFrames();
				that._trigger( "dragStart", event, filteredUi( ui ) );
			},
			drag: function( event, ui ) {
				that._trigger( "drag", event, filteredUi( ui ) );
			},
			stop: function( event, ui ) {
				var left = ui.offset.left - that.document.scrollLeft(),
					top = ui.offset.top - that.document.scrollTop();

				options.position = {
					my: "left top",
					at: "left" + ( left >= 0 ? "+" : "" ) + left + " " +
						"top" + ( top >= 0 ? "+" : "" ) + top,
					of: that.window
				};
				that._removeClass( $( this ), "ui-dialog-dragging" );
				that._unblockFrames();
				that._trigger( "dragStop", event, filteredUi( ui ) );
			}
		} );
	},

	_makeResizable: function() {
		var that = this,
			options = this.options,
			handles = options.resizable,

			// .ui-resizable has position: relative defined in the stylesheet
			// but dialogs have to use absolute or fixed positioning
			position = this.uiDialog.css( "position" ),
			resizeHandles = typeof handles === "string" ?
				handles :
				"n,e,s,w,se,sw,ne,nw";

		function filteredUi( ui ) {
			return {
				originalPosition: ui.originalPosition,
				originalSize: ui.originalSize,
				position: ui.position,
				size: ui.size
			};
		}

		this.uiDialog.resizable( {
			cancel: ".ui-dialog-content",
			containment: "document",
			alsoResize: this.element,
			maxWidth: options.maxWidth,
			maxHeight: options.maxHeight,
			minWidth: options.minWidth,
			minHeight: this._minHeight(),
			handles: resizeHandles,
			start: function( event, ui ) {
				that._addClass( $( this ), "ui-dialog-resizing" );
				that._blockFrames();
				that._trigger( "resizeStart", event, filteredUi( ui ) );
			},
			resize: function( event, ui ) {
				that._trigger( "resize", event, filteredUi( ui ) );
			},
			stop: function( event, ui ) {
				var offset = that.uiDialog.offset(),
					left = offset.left - that.document.scrollLeft(),
					top = offset.top - that.document.scrollTop();

				options.height = that.uiDialog.height();
				options.width = that.uiDialog.width();
				options.position = {
					my: "left top",
					at: "left" + ( left >= 0 ? "+" : "" ) + left + " " +
						"top" + ( top >= 0 ? "+" : "" ) + top,
					of: that.window
				};
				that._removeClass( $( this ), "ui-dialog-resizing" );
				that._unblockFrames();
				that._trigger( "resizeStop", event, filteredUi( ui ) );
			}
		} )
			.css( "position", position );
	},

	_trackFocus: function() {
		this._on( this.widget(), {
			focusin: function( event ) {
				this._makeFocusTarget();
				this._focusedElement = $( event.target );
			}
		} );
	},

	_makeFocusTarget: function() {
		this._untrackInstance();
		this._trackingInstances().unshift( this );
	},

	_untrackInstance: function() {
		var instances = this._trackingInstances(),
			exists = $.inArray( this, instances );
		if ( exists !== -1 ) {
			instances.splice( exists, 1 );
		}
	},

	_trackingInstances: function() {
		var instances = this.document.data( "ui-dialog-instances" );
		if ( !instances ) {
			instances = [];
			this.document.data( "ui-dialog-instances", instances );
		}
		return instances;
	},

	_minHeight: function() {
		var options = this.options;

		return options.height === "auto" ?
			options.minHeight :
			Math.min( options.minHeight, options.height );
	},

	_position: function() {

		// Need to show the dialog to get the actual offset in the position plugin
		var isVisible = this.uiDialog.is( ":visible" );
		if ( !isVisible ) {
			this.uiDialog.show();
		}
		this.uiDialog.position( this.options.position );
		if ( !isVisible ) {
			this.uiDialog.hide();
		}
	},

	_setOptions: function( options ) {
		var that = this,
			resize = false,
			resizableOptions = {};

		$.each( options, function( key, value ) {
			that._setOption( key, value );

			if ( key in that.sizeRelatedOptions ) {
				resize = true;
			}
			if ( key in that.resizableRelatedOptions ) {
				resizableOptions[ key ] = value;
			}
		} );

		if ( resize ) {
			this._size();
			this._position();
		}
		if ( this.uiDialog.is( ":data(ui-resizable)" ) ) {
			this.uiDialog.resizable( "option", resizableOptions );
		}
	},

	_setOption: function( key, value ) {
		var isDraggable, isResizable,
			uiDialog = this.uiDialog;

		if ( key === "disabled" ) {
			return;
		}

		this._super( key, value );

		if ( key === "appendTo" ) {
			this.uiDialog.appendTo( this._appendTo() );
		}

		if ( key === "buttons" ) {
			this._createButtons();
		}

		if ( key === "closeText" ) {
			this.uiDialogTitlebarClose.button( {

				// Ensure that we always pass a string
				label: $( "<a>" ).text( "" + this.options.closeText ).html()
			} );
		}

		if ( key === "draggable" ) {
			isDraggable = uiDialog.is( ":data(ui-draggable)" );
			if ( isDraggable && !value ) {
				uiDialog.draggable( "destroy" );
			}

			if ( !isDraggable && value ) {
				this._makeDraggable();
			}
		}

		if ( key === "position" ) {
			this._position();
		}

		if ( key === "resizable" ) {

			// currently resizable, becoming non-resizable
			isResizable = uiDialog.is( ":data(ui-resizable)" );
			if ( isResizable && !value ) {
				uiDialog.resizable( "destroy" );
			}

			// Currently resizable, changing handles
			if ( isResizable && typeof value === "string" ) {
				uiDialog.resizable( "option", "handles", value );
			}

			// Currently non-resizable, becoming resizable
			if ( !isResizable && value !== false ) {
				this._makeResizable();
			}
		}

		if ( key === "title" ) {
			this._title( this.uiDialogTitlebar.find( ".ui-dialog-title" ) );
		}
	},

	_size: function() {

		// If the user has resized the dialog, the .ui-dialog and .ui-dialog-content
		// divs will both have width and height set, so we need to reset them
		var nonContentHeight, minContentHeight, maxContentHeight,
			options = this.options;

		// Reset content sizing
		this.element.show().css( {
			width: "auto",
			minHeight: 0,
			maxHeight: "none",
			height: 0
		} );

		if ( options.minWidth > options.width ) {
			options.width = options.minWidth;
		}

		// Reset wrapper sizing
		// determine the height of all the non-content elements
		nonContentHeight = this.uiDialog.css( {
			height: "auto",
			width: options.width
		} )
			.outerHeight();
		minContentHeight = Math.max( 0, options.minHeight - nonContentHeight );
		maxContentHeight = typeof options.maxHeight === "number" ?
			Math.max( 0, options.maxHeight - nonContentHeight ) :
			"none";

		if ( options.height === "auto" ) {
			this.element.css( {
				minHeight: minContentHeight,
				maxHeight: maxContentHeight,
				height: "auto"
			} );
		} else {
			this.element.height( Math.max( 0, options.height - nonContentHeight ) );
		}

		if ( this.uiDialog.is( ":data(ui-resizable)" ) ) {
			this.uiDialog.resizable( "option", "minHeight", this._minHeight() );
		}
	},

	_blockFrames: function() {
		this.iframeBlocks = this.document.find( "iframe" ).map( function() {
			var iframe = $( this );

			return $( "<div>" )
				.css( {
					position: "absolute",
					width: iframe.outerWidth(),
					height: iframe.outerHeight()
				} )
				.appendTo( iframe.parent() )
				.offset( iframe.offset() )[ 0 ];
		} );
	},

	_unblockFrames: function() {
		if ( this.iframeBlocks ) {
			this.iframeBlocks.remove();
			delete this.iframeBlocks;
		}
	},

	_allowInteraction: function( event ) {
		if ( $( event.target ).closest( ".ui-dialog" ).length ) {
			return true;
		}

		// TODO: Remove hack when datepicker implements
		// the .ui-front logic (#8989)
		return !!$( event.target ).closest( ".ui-datepicker" ).length;
	},

	_createOverlay: function() {
		if ( !this.options.modal ) {
			return;
		}

		// We use a delay in case the overlay is created from an
		// event that we're going to be cancelling (#2804)
		var isOpening = true;
		this._delay( function() {
			isOpening = false;
		} );

		if ( !this.document.data( "ui-dialog-overlays" ) ) {

			// Prevent use of anchors and inputs
			// Using _on() for an event handler shared across many instances is
			// safe because the dialogs stack and must be closed in reverse order
			this._on( this.document, {
				focusin: function( event ) {
					if ( isOpening ) {
						return;
					}

					if ( !this._allowInteraction( event ) ) {
						event.preventDefault();
						this._trackingInstances()[ 0 ]._focusTabbable();
					}
				}
			} );
		}

		this.overlay = $( "<div>" )
			.appendTo( this._appendTo() );

		this._addClass( this.overlay, null, "ui-widget-overlay ui-front" );
		this._on( this.overlay, {
			mousedown: "_keepFocus"
		} );
		this.document.data( "ui-dialog-overlays",
			( this.document.data( "ui-dialog-overlays" ) || 0 ) + 1 );
	},

	_destroyOverlay: function() {
		if ( !this.options.modal ) {
			return;
		}

		if ( this.overlay ) {
			var overlays = this.document.data( "ui-dialog-overlays" ) - 1;

			if ( !overlays ) {
				this._off( this.document, "focusin" );
				this.document.removeData( "ui-dialog-overlays" );
			} else {
				this.document.data( "ui-dialog-overlays", overlays );
			}

			this.overlay.remove();
			this.overlay = null;
		}
	}
} );

// DEPRECATED
// TODO: switch return back to widget declaration at top of file when this is removed
if ( $.uiBackCompat !== false ) {

	// Backcompat for dialogClass option
	$.widget( "ui.dialog", $.ui.dialog, {
		options: {
			dialogClass: ""
		},
		_createWrapper: function() {
			this._super();
			this.uiDialog.addClass( this.options.dialogClass );
		},
		_setOption: function( key, value ) {
			if ( key === "dialogClass" ) {
				this.uiDialog
					.removeClass( this.options.dialogClass )
					.addClass( value );
			}
			this._superApply( arguments );
		}
	} );
}

var widgetsDialog = $.ui.dialog;


/*!
 * jQuery UI Droppable 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Droppable
//>>group: Interactions
//>>description: Enables drop targets for draggable elements.
//>>docs: http://api.jqueryui.com/droppable/
//>>demos: http://jqueryui.com/droppable/



$.widget( "ui.droppable", {
	version: "1.12.1",
	widgetEventPrefix: "drop",
	options: {
		accept: "*",
		addClasses: true,
		greedy: false,
		scope: "default",
		tolerance: "intersect",

		// Callbacks
		activate: null,
		deactivate: null,
		drop: null,
		out: null,
		over: null
	},
	_create: function() {

		var proportions,
			o = this.options,
			accept = o.accept;

		this.isover = false;
		this.isout = true;

		this.accept = $.isFunction( accept ) ? accept : function( d ) {
			return d.is( accept );
		};

		this.proportions = function( /* valueToWrite */ ) {
			if ( arguments.length ) {

				// Store the droppable's proportions
				proportions = arguments[ 0 ];
			} else {

				// Retrieve or derive the droppable's proportions
				return proportions ?
					proportions :
					proportions = {
						width: this.element[ 0 ].offsetWidth,
						height: this.element[ 0 ].offsetHeight
					};
			}
		};

		this._addToManager( o.scope );

		o.addClasses && this._addClass( "ui-droppable" );

	},

	_addToManager: function( scope ) {

		// Add the reference and positions to the manager
		$.ui.ddmanager.droppables[ scope ] = $.ui.ddmanager.droppables[ scope ] || [];
		$.ui.ddmanager.droppables[ scope ].push( this );
	},

	_splice: function( drop ) {
		var i = 0;
		for ( ; i < drop.length; i++ ) {
			if ( drop[ i ] === this ) {
				drop.splice( i, 1 );
			}
		}
	},

	_destroy: function() {
		var drop = $.ui.ddmanager.droppables[ this.options.scope ];

		this._splice( drop );
	},

	_setOption: function( key, value ) {

		if ( key === "accept" ) {
			this.accept = $.isFunction( value ) ? value : function( d ) {
				return d.is( value );
			};
		} else if ( key === "scope" ) {
			var drop = $.ui.ddmanager.droppables[ this.options.scope ];

			this._splice( drop );
			this._addToManager( value );
		}

		this._super( key, value );
	},

	_activate: function( event ) {
		var draggable = $.ui.ddmanager.current;

		this._addActiveClass();
		if ( draggable ) {
			this._trigger( "activate", event, this.ui( draggable ) );
		}
	},

	_deactivate: function( event ) {
		var draggable = $.ui.ddmanager.current;

		this._removeActiveClass();
		if ( draggable ) {
			this._trigger( "deactivate", event, this.ui( draggable ) );
		}
	},

	_over: function( event ) {

		var draggable = $.ui.ddmanager.current;

		// Bail if draggable and droppable are same element
		if ( !draggable || ( draggable.currentItem ||
				draggable.element )[ 0 ] === this.element[ 0 ] ) {
			return;
		}

		if ( this.accept.call( this.element[ 0 ], ( draggable.currentItem ||
				draggable.element ) ) ) {
			this._addHoverClass();
			this._trigger( "over", event, this.ui( draggable ) );
		}

	},

	_out: function( event ) {

		var draggable = $.ui.ddmanager.current;

		// Bail if draggable and droppable are same element
		if ( !draggable || ( draggable.currentItem ||
				draggable.element )[ 0 ] === this.element[ 0 ] ) {
			return;
		}

		if ( this.accept.call( this.element[ 0 ], ( draggable.currentItem ||
				draggable.element ) ) ) {
			this._removeHoverClass();
			this._trigger( "out", event, this.ui( draggable ) );
		}

	},

	_drop: function( event, custom ) {

		var draggable = custom || $.ui.ddmanager.current,
			childrenIntersection = false;

		// Bail if draggable and droppable are same element
		if ( !draggable || ( draggable.currentItem ||
				draggable.element )[ 0 ] === this.element[ 0 ] ) {
			return false;
		}

		this.element
			.find( ":data(ui-droppable)" )
			.not( ".ui-draggable-dragging" )
			.each( function() {
				var inst = $( this ).droppable( "instance" );
				if (
					inst.options.greedy &&
					!inst.options.disabled &&
					inst.options.scope === draggable.options.scope &&
					inst.accept.call(
						inst.element[ 0 ], ( draggable.currentItem || draggable.element )
					) &&
					intersect(
						draggable,
						$.extend( inst, { offset: inst.element.offset() } ),
						inst.options.tolerance, event
					)
				) {
					childrenIntersection = true;
					return false; }
			} );
		if ( childrenIntersection ) {
			return false;
		}

		if ( this.accept.call( this.element[ 0 ],
				( draggable.currentItem || draggable.element ) ) ) {
			this._removeActiveClass();
			this._removeHoverClass();

			this._trigger( "drop", event, this.ui( draggable ) );
			return this.element;
		}

		return false;

	},

	ui: function( c ) {
		return {
			draggable: ( c.currentItem || c.element ),
			helper: c.helper,
			position: c.position,
			offset: c.positionAbs
		};
	},

	// Extension points just to make backcompat sane and avoid duplicating logic
	// TODO: Remove in 1.13 along with call to it below
	_addHoverClass: function() {
		this._addClass( "ui-droppable-hover" );
	},

	_removeHoverClass: function() {
		this._removeClass( "ui-droppable-hover" );
	},

	_addActiveClass: function() {
		this._addClass( "ui-droppable-active" );
	},

	_removeActiveClass: function() {
		this._removeClass( "ui-droppable-active" );
	}
} );

var intersect = $.ui.intersect = ( function() {
	function isOverAxis( x, reference, size ) {
		return ( x >= reference ) && ( x < ( reference + size ) );
	}

	return function( draggable, droppable, toleranceMode, event ) {

		if ( !droppable.offset ) {
			return false;
		}

		var x1 = ( draggable.positionAbs ||
				draggable.position.absolute ).left + draggable.margins.left,
			y1 = ( draggable.positionAbs ||
				draggable.position.absolute ).top + draggable.margins.top,
			x2 = x1 + draggable.helperProportions.width,
			y2 = y1 + draggable.helperProportions.height,
			l = droppable.offset.left,
			t = droppable.offset.top,
			r = l + droppable.proportions().width,
			b = t + droppable.proportions().height;

		switch ( toleranceMode ) {
		case "fit":
			return ( l <= x1 && x2 <= r && t <= y1 && y2 <= b );
		case "intersect":
			return ( l < x1 + ( draggable.helperProportions.width / 2 ) && // Right Half
				x2 - ( draggable.helperProportions.width / 2 ) < r && // Left Half
				t < y1 + ( draggable.helperProportions.height / 2 ) && // Bottom Half
				y2 - ( draggable.helperProportions.height / 2 ) < b ); // Top Half
		case "pointer":
			return isOverAxis( event.pageY, t, droppable.proportions().height ) &&
				isOverAxis( event.pageX, l, droppable.proportions().width );
		case "touch":
			return (
				( y1 >= t && y1 <= b ) || // Top edge touching
				( y2 >= t && y2 <= b ) || // Bottom edge touching
				( y1 < t && y2 > b ) // Surrounded vertically
			) && (
				( x1 >= l && x1 <= r ) || // Left edge touching
				( x2 >= l && x2 <= r ) || // Right edge touching
				( x1 < l && x2 > r ) // Surrounded horizontally
			);
		default:
			return false;
		}
	};
} )();

/*
	This manager tracks offsets of draggables and droppables
*/
$.ui.ddmanager = {
	current: null,
	droppables: { "default": [] },
	prepareOffsets: function( t, event ) {

		var i, j,
			m = $.ui.ddmanager.droppables[ t.options.scope ] || [],
			type = event ? event.type : null, // workaround for #2317
			list = ( t.currentItem || t.element ).find( ":data(ui-droppable)" ).addBack();

		droppablesLoop: for ( i = 0; i < m.length; i++ ) {

			// No disabled and non-accepted
			if ( m[ i ].options.disabled || ( t && !m[ i ].accept.call( m[ i ].element[ 0 ],
					( t.currentItem || t.element ) ) ) ) {
				continue;
			}

			// Filter out elements in the current dragged item
			for ( j = 0; j < list.length; j++ ) {
				if ( list[ j ] === m[ i ].element[ 0 ] ) {
					m[ i ].proportions().height = 0;
					continue droppablesLoop;
				}
			}

			m[ i ].visible = m[ i ].element.css( "display" ) !== "none";
			if ( !m[ i ].visible ) {
				continue;
			}

			// Activate the droppable if used directly from draggables
			if ( type === "mousedown" ) {
				m[ i ]._activate.call( m[ i ], event );
			}

			m[ i ].offset = m[ i ].element.offset();
			m[ i ].proportions( {
				width: m[ i ].element[ 0 ].offsetWidth,
				height: m[ i ].element[ 0 ].offsetHeight
			} );

		}

	},
	drop: function( draggable, event ) {

		var dropped = false;

		// Create a copy of the droppables in case the list changes during the drop (#9116)
		$.each( ( $.ui.ddmanager.droppables[ draggable.options.scope ] || [] ).slice(), function() {

			if ( !this.options ) {
				return;
			}
			if ( !this.options.disabled && this.visible &&
					intersect( draggable, this, this.options.tolerance, event ) ) {
				dropped = this._drop.call( this, event ) || dropped;
			}

			if ( !this.options.disabled && this.visible && this.accept.call( this.element[ 0 ],
					( draggable.currentItem || draggable.element ) ) ) {
				this.isout = true;
				this.isover = false;
				this._deactivate.call( this, event );
			}

		} );
		return dropped;

	},
	dragStart: function( draggable, event ) {

		// Listen for scrolling so that if the dragging causes scrolling the position of the
		// droppables can be recalculated (see #5003)
		draggable.element.parentsUntil( "body" ).on( "scroll.droppable", function() {
			if ( !draggable.options.refreshPositions ) {
				$.ui.ddmanager.prepareOffsets( draggable, event );
			}
		} );
	},
	drag: function( draggable, event ) {

		// If you have a highly dynamic page, you might try this option. It renders positions
		// every time you move the mouse.
		if ( draggable.options.refreshPositions ) {
			$.ui.ddmanager.prepareOffsets( draggable, event );
		}

		// Run through all droppables and check their positions based on specific tolerance options
		$.each( $.ui.ddmanager.droppables[ draggable.options.scope ] || [], function() {

			if ( this.options.disabled || this.greedyChild || !this.visible ) {
				return;
			}

			var parentInstance, scope, parent,
				intersects = intersect( draggable, this, this.options.tolerance, event ),
				c = !intersects && this.isover ?
					"isout" :
					( intersects && !this.isover ? "isover" : null );
			if ( !c ) {
				return;
			}

			if ( this.options.greedy ) {

				// find droppable parents with same scope
				scope = this.options.scope;
				parent = this.element.parents( ":data(ui-droppable)" ).filter( function() {
					return $( this ).droppable( "instance" ).options.scope === scope;
				} );

				if ( parent.length ) {
					parentInstance = $( parent[ 0 ] ).droppable( "instance" );
					parentInstance.greedyChild = ( c === "isover" );
				}
			}

			// We just moved into a greedy child
			if ( parentInstance && c === "isover" ) {
				parentInstance.isover = false;
				parentInstance.isout = true;
				parentInstance._out.call( parentInstance, event );
			}

			this[ c ] = true;
			this[ c === "isout" ? "isover" : "isout" ] = false;
			this[ c === "isover" ? "_over" : "_out" ].call( this, event );

			// We just moved out of a greedy child
			if ( parentInstance && c === "isout" ) {
				parentInstance.isout = false;
				parentInstance.isover = true;
				parentInstance._over.call( parentInstance, event );
			}
		} );

	},
	dragStop: function( draggable, event ) {
		draggable.element.parentsUntil( "body" ).off( "scroll.droppable" );

		// Call prepareOffsets one final time since IE does not fire return scroll events when
		// overflow was caused by drag (see #5003)
		if ( !draggable.options.refreshPositions ) {
			$.ui.ddmanager.prepareOffsets( draggable, event );
		}
	}
};

// DEPRECATED
// TODO: switch return back to widget declaration at top of file when this is removed
if ( $.uiBackCompat !== false ) {

	// Backcompat for activeClass and hoverClass options
	$.widget( "ui.droppable", $.ui.droppable, {
		options: {
			hoverClass: false,
			activeClass: false
		},
		_addActiveClass: function() {
			this._super();
			if ( this.options.activeClass ) {
				this.element.addClass( this.options.activeClass );
			}
		},
		_removeActiveClass: function() {
			this._super();
			if ( this.options.activeClass ) {
				this.element.removeClass( this.options.activeClass );
			}
		},
		_addHoverClass: function() {
			this._super();
			if ( this.options.hoverClass ) {
				this.element.addClass( this.options.hoverClass );
			}
		},
		_removeHoverClass: function() {
			this._super();
			if ( this.options.hoverClass ) {
				this.element.removeClass( this.options.hoverClass );
			}
		}
	} );
}

var widgetsDroppable = $.ui.droppable;


/*!
 * jQuery UI Progressbar 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Progressbar
//>>group: Widgets
// jscs:disable maximumLineLength
//>>description: Displays a status indicator for loading state, standard percentage, and other progress indicators.
// jscs:enable maximumLineLength
//>>docs: http://api.jqueryui.com/progressbar/
//>>demos: http://jqueryui.com/progressbar/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/progressbar.css
//>>css.theme: ../../themes/base/theme.css



var widgetsProgressbar = $.widget( "ui.progressbar", {
	version: "1.12.1",
	options: {
		classes: {
			"ui-progressbar": "ui-corner-all",
			"ui-progressbar-value": "ui-corner-left",
			"ui-progressbar-complete": "ui-corner-right"
		},
		max: 100,
		value: 0,

		change: null,
		complete: null
	},

	min: 0,

	_create: function() {

		// Constrain initial value
		this.oldValue = this.options.value = this._constrainedValue();

		this.element.attr( {

			// Only set static values; aria-valuenow and aria-valuemax are
			// set inside _refreshValue()
			role: "progressbar",
			"aria-valuemin": this.min
		} );
		this._addClass( "ui-progressbar", "ui-widget ui-widget-content" );

		this.valueDiv = $( "<div>" ).appendTo( this.element );
		this._addClass( this.valueDiv, "ui-progressbar-value", "ui-widget-header" );
		this._refreshValue();
	},

	_destroy: function() {
		this.element.removeAttr( "role aria-valuemin aria-valuemax aria-valuenow" );

		this.valueDiv.remove();
	},

	value: function( newValue ) {
		if ( newValue === undefined ) {
			return this.options.value;
		}

		this.options.value = this._constrainedValue( newValue );
		this._refreshValue();
	},

	_constrainedValue: function( newValue ) {
		if ( newValue === undefined ) {
			newValue = this.options.value;
		}

		this.indeterminate = newValue === false;

		// Sanitize value
		if ( typeof newValue !== "number" ) {
			newValue = 0;
		}

		return this.indeterminate ? false :
			Math.min( this.options.max, Math.max( this.min, newValue ) );
	},

	_setOptions: function( options ) {

		// Ensure "value" option is set after other values (like max)
		var value = options.value;
		delete options.value;

		this._super( options );

		this.options.value = this._constrainedValue( value );
		this._refreshValue();
	},

	_setOption: function( key, value ) {
		if ( key === "max" ) {

			// Don't allow a max less than min
			value = Math.max( this.min, value );
		}
		this._super( key, value );
	},

	_setOptionDisabled: function( value ) {
		this._super( value );

		this.element.attr( "aria-disabled", value );
		this._toggleClass( null, "ui-state-disabled", !!value );
	},

	_percentage: function() {
		return this.indeterminate ?
			100 :
			100 * ( this.options.value - this.min ) / ( this.options.max - this.min );
	},

	_refreshValue: function() {
		var value = this.options.value,
			percentage = this._percentage();

		this.valueDiv
			.toggle( this.indeterminate || value > this.min )
			.width( percentage.toFixed( 0 ) + "%" );

		this
			._toggleClass( this.valueDiv, "ui-progressbar-complete", null,
				value === this.options.max )
			._toggleClass( "ui-progressbar-indeterminate", null, this.indeterminate );

		if ( this.indeterminate ) {
			this.element.removeAttr( "aria-valuenow" );
			if ( !this.overlayDiv ) {
				this.overlayDiv = $( "<div>" ).appendTo( this.valueDiv );
				this._addClass( this.overlayDiv, "ui-progressbar-overlay" );
			}
		} else {
			this.element.attr( {
				"aria-valuemax": this.options.max,
				"aria-valuenow": value
			} );
			if ( this.overlayDiv ) {
				this.overlayDiv.remove();
				this.overlayDiv = null;
			}
		}

		if ( this.oldValue !== value ) {
			this.oldValue = value;
			this._trigger( "change" );
		}
		if ( value === this.options.max ) {
			this._trigger( "complete" );
		}
	}
} );


/*!
 * jQuery UI Selectable 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Selectable
//>>group: Interactions
//>>description: Allows groups of elements to be selected with the mouse.
//>>docs: http://api.jqueryui.com/selectable/
//>>demos: http://jqueryui.com/selectable/
//>>css.structure: ../../themes/base/selectable.css



var widgetsSelectable = $.widget( "ui.selectable", $.ui.mouse, {
	version: "1.12.1",
	options: {
		appendTo: "body",
		autoRefresh: true,
		distance: 0,
		filter: "*",
		tolerance: "touch",

		// Callbacks
		selected: null,
		selecting: null,
		start: null,
		stop: null,
		unselected: null,
		unselecting: null
	},
	_create: function() {
		var that = this;

		this._addClass( "ui-selectable" );

		this.dragged = false;

		// Cache selectee children based on filter
		this.refresh = function() {
			that.elementPos = $( that.element[ 0 ] ).offset();
			that.selectees = $( that.options.filter, that.element[ 0 ] );
			that._addClass( that.selectees, "ui-selectee" );
			that.selectees.each( function() {
				var $this = $( this ),
					selecteeOffset = $this.offset(),
					pos = {
						left: selecteeOffset.left - that.elementPos.left,
						top: selecteeOffset.top - that.elementPos.top
					};
				$.data( this, "selectable-item", {
					element: this,
					$element: $this,
					left: pos.left,
					top: pos.top,
					right: pos.left + $this.outerWidth(),
					bottom: pos.top + $this.outerHeight(),
					startselected: false,
					selected: $this.hasClass( "ui-selected" ),
					selecting: $this.hasClass( "ui-selecting" ),
					unselecting: $this.hasClass( "ui-unselecting" )
				} );
			} );
		};
		this.refresh();

		this._mouseInit();

		this.helper = $( "<div>" );
		this._addClass( this.helper, "ui-selectable-helper" );
	},

	_destroy: function() {
		this.selectees.removeData( "selectable-item" );
		this._mouseDestroy();
	},

	_mouseStart: function( event ) {
		var that = this,
			options = this.options;

		this.opos = [ event.pageX, event.pageY ];
		this.elementPos = $( this.element[ 0 ] ).offset();

		if ( this.options.disabled ) {
			return;
		}

		this.selectees = $( options.filter, this.element[ 0 ] );

		this._trigger( "start", event );

		$( options.appendTo ).append( this.helper );

		// position helper (lasso)
		this.helper.css( {
			"left": event.pageX,
			"top": event.pageY,
			"width": 0,
			"height": 0
		} );

		if ( options.autoRefresh ) {
			this.refresh();
		}

		this.selectees.filter( ".ui-selected" ).each( function() {
			var selectee = $.data( this, "selectable-item" );
			selectee.startselected = true;
			if ( !event.metaKey && !event.ctrlKey ) {
				that._removeClass( selectee.$element, "ui-selected" );
				selectee.selected = false;
				that._addClass( selectee.$element, "ui-unselecting" );
				selectee.unselecting = true;

				// selectable UNSELECTING callback
				that._trigger( "unselecting", event, {
					unselecting: selectee.element
				} );
			}
		} );

		$( event.target ).parents().addBack().each( function() {
			var doSelect,
				selectee = $.data( this, "selectable-item" );
			if ( selectee ) {
				doSelect = ( !event.metaKey && !event.ctrlKey ) ||
					!selectee.$element.hasClass( "ui-selected" );
				that._removeClass( selectee.$element, doSelect ? "ui-unselecting" : "ui-selected" )
					._addClass( selectee.$element, doSelect ? "ui-selecting" : "ui-unselecting" );
				selectee.unselecting = !doSelect;
				selectee.selecting = doSelect;
				selectee.selected = doSelect;

				// selectable (UN)SELECTING callback
				if ( doSelect ) {
					that._trigger( "selecting", event, {
						selecting: selectee.element
					} );
				} else {
					that._trigger( "unselecting", event, {
						unselecting: selectee.element
					} );
				}
				return false;
			}
		} );

	},

	_mouseDrag: function( event ) {

		this.dragged = true;

		if ( this.options.disabled ) {
			return;
		}

		var tmp,
			that = this,
			options = this.options,
			x1 = this.opos[ 0 ],
			y1 = this.opos[ 1 ],
			x2 = event.pageX,
			y2 = event.pageY;

		if ( x1 > x2 ) { tmp = x2; x2 = x1; x1 = tmp; }
		if ( y1 > y2 ) { tmp = y2; y2 = y1; y1 = tmp; }
		this.helper.css( { left: x1, top: y1, width: x2 - x1, height: y2 - y1 } );

		this.selectees.each( function() {
			var selectee = $.data( this, "selectable-item" ),
				hit = false,
				offset = {};

			//prevent helper from being selected if appendTo: selectable
			if ( !selectee || selectee.element === that.element[ 0 ] ) {
				return;
			}

			offset.left   = selectee.left   + that.elementPos.left;
			offset.right  = selectee.right  + that.elementPos.left;
			offset.top    = selectee.top    + that.elementPos.top;
			offset.bottom = selectee.bottom + that.elementPos.top;

			if ( options.tolerance === "touch" ) {
				hit = ( !( offset.left > x2 || offset.right < x1 || offset.top > y2 ||
                    offset.bottom < y1 ) );
			} else if ( options.tolerance === "fit" ) {
				hit = ( offset.left > x1 && offset.right < x2 && offset.top > y1 &&
                    offset.bottom < y2 );
			}

			if ( hit ) {

				// SELECT
				if ( selectee.selected ) {
					that._removeClass( selectee.$element, "ui-selected" );
					selectee.selected = false;
				}
				if ( selectee.unselecting ) {
					that._removeClass( selectee.$element, "ui-unselecting" );
					selectee.unselecting = false;
				}
				if ( !selectee.selecting ) {
					that._addClass( selectee.$element, "ui-selecting" );
					selectee.selecting = true;

					// selectable SELECTING callback
					that._trigger( "selecting", event, {
						selecting: selectee.element
					} );
				}
			} else {

				// UNSELECT
				if ( selectee.selecting ) {
					if ( ( event.metaKey || event.ctrlKey ) && selectee.startselected ) {
						that._removeClass( selectee.$element, "ui-selecting" );
						selectee.selecting = false;
						that._addClass( selectee.$element, "ui-selected" );
						selectee.selected = true;
					} else {
						that._removeClass( selectee.$element, "ui-selecting" );
						selectee.selecting = false;
						if ( selectee.startselected ) {
							that._addClass( selectee.$element, "ui-unselecting" );
							selectee.unselecting = true;
						}

						// selectable UNSELECTING callback
						that._trigger( "unselecting", event, {
							unselecting: selectee.element
						} );
					}
				}
				if ( selectee.selected ) {
					if ( !event.metaKey && !event.ctrlKey && !selectee.startselected ) {
						that._removeClass( selectee.$element, "ui-selected" );
						selectee.selected = false;

						that._addClass( selectee.$element, "ui-unselecting" );
						selectee.unselecting = true;

						// selectable UNSELECTING callback
						that._trigger( "unselecting", event, {
							unselecting: selectee.element
						} );
					}
				}
			}
		} );

		return false;
	},

	_mouseStop: function( event ) {
		var that = this;

		this.dragged = false;

		$( ".ui-unselecting", this.element[ 0 ] ).each( function() {
			var selectee = $.data( this, "selectable-item" );
			that._removeClass( selectee.$element, "ui-unselecting" );
			selectee.unselecting = false;
			selectee.startselected = false;
			that._trigger( "unselected", event, {
				unselected: selectee.element
			} );
		} );
		$( ".ui-selecting", this.element[ 0 ] ).each( function() {
			var selectee = $.data( this, "selectable-item" );
			that._removeClass( selectee.$element, "ui-selecting" )
				._addClass( selectee.$element, "ui-selected" );
			selectee.selecting = false;
			selectee.selected = true;
			selectee.startselected = true;
			that._trigger( "selected", event, {
				selected: selectee.element
			} );
		} );
		this._trigger( "stop", event );

		this.helper.remove();

		return false;
	}

} );


/*!
 * jQuery UI Selectmenu 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Selectmenu
//>>group: Widgets
// jscs:disable maximumLineLength
//>>description: Duplicates and extends the functionality of a native HTML select element, allowing it to be customizable in behavior and appearance far beyond the limitations of a native select.
// jscs:enable maximumLineLength
//>>docs: http://api.jqueryui.com/selectmenu/
//>>demos: http://jqueryui.com/selectmenu/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/selectmenu.css, ../../themes/base/button.css
//>>css.theme: ../../themes/base/theme.css



var widgetsSelectmenu = $.widget( "ui.selectmenu", [ $.ui.formResetMixin, {
	version: "1.12.1",
	defaultElement: "<select>",
	options: {
		appendTo: null,
		classes: {
			"ui-selectmenu-button-open": "ui-corner-top",
			"ui-selectmenu-button-closed": "ui-corner-all"
		},
		disabled: null,
		icons: {
			button: "ui-icon-triangle-1-s"
		},
		position: {
			my: "left top",
			at: "left bottom",
			collision: "none"
		},
		width: false,

		// Callbacks
		change: null,
		close: null,
		focus: null,
		open: null,
		select: null
	},

	_create: function() {
		var selectmenuId = this.element.uniqueId().attr( "id" );
		this.ids = {
			element: selectmenuId,
			button: selectmenuId + "-button",
			menu: selectmenuId + "-menu"
		};

		this._drawButton();
		this._drawMenu();
		this._bindFormResetHandler();

		this._rendered = false;
		this.menuItems = $();
	},

	_drawButton: function() {
		var icon,
			that = this,
			item = this._parseOption(
				this.element.find( "option:selected" ),
				this.element[ 0 ].selectedIndex
			);

		// Associate existing label with the new button
		this.labels = this.element.labels().attr( "for", this.ids.button );
		this._on( this.labels, {
			click: function( event ) {
				this.button.focus();
				event.preventDefault();
			}
		} );

		// Hide original select element
		this.element.hide();

		// Create button
		this.button = $( "<span>", {
			tabindex: this.options.disabled ? -1 : 0,
			id: this.ids.button,
			role: "combobox",
			"aria-expanded": "false",
			"aria-autocomplete": "list",
			"aria-owns": this.ids.menu,
			"aria-haspopup": "true",
			title: this.element.attr( "title" )
		} )
			.insertAfter( this.element );

		this._addClass( this.button, "ui-selectmenu-button ui-selectmenu-button-closed",
			"ui-button ui-widget" );

		icon = $( "<span>" ).appendTo( this.button );
		this._addClass( icon, "ui-selectmenu-icon", "ui-icon " + this.options.icons.button );
		this.buttonItem = this._renderButtonItem( item )
			.appendTo( this.button );

		if ( this.options.width !== false ) {
			this._resizeButton();
		}

		this._on( this.button, this._buttonEvents );
		this.button.one( "focusin", function() {

			// Delay rendering the menu items until the button receives focus.
			// The menu may have already been rendered via a programmatic open.
			if ( !that._rendered ) {
				that._refreshMenu();
			}
		} );
	},

	_drawMenu: function() {
		var that = this;

		// Create menu
		this.menu = $( "<ul>", {
			"aria-hidden": "true",
			"aria-labelledby": this.ids.button,
			id: this.ids.menu
		} );

		// Wrap menu
		this.menuWrap = $( "<div>" ).append( this.menu );
		this._addClass( this.menuWrap, "ui-selectmenu-menu", "ui-front" );
		this.menuWrap.appendTo( this._appendTo() );

		// Initialize menu widget
		this.menuInstance = this.menu
			.menu( {
				classes: {
					"ui-menu": "ui-corner-bottom"
				},
				role: "listbox",
				select: function( event, ui ) {
					event.preventDefault();

					// Support: IE8
					// If the item was selected via a click, the text selection
					// will be destroyed in IE
					that._setSelection();

					that._select( ui.item.data( "ui-selectmenu-item" ), event );
				},
				focus: function( event, ui ) {
					var item = ui.item.data( "ui-selectmenu-item" );

					// Prevent inital focus from firing and check if its a newly focused item
					if ( that.focusIndex != null && item.index !== that.focusIndex ) {
						that._trigger( "focus", event, { item: item } );
						if ( !that.isOpen ) {
							that._select( item, event );
						}
					}
					that.focusIndex = item.index;

					that.button.attr( "aria-activedescendant",
						that.menuItems.eq( item.index ).attr( "id" ) );
				}
			} )
			.menu( "instance" );

		// Don't close the menu on mouseleave
		this.menuInstance._off( this.menu, "mouseleave" );

		// Cancel the menu's collapseAll on document click
		this.menuInstance._closeOnDocumentClick = function() {
			return false;
		};

		// Selects often contain empty items, but never contain dividers
		this.menuInstance._isDivider = function() {
			return false;
		};
	},

	refresh: function() {
		this._refreshMenu();
		this.buttonItem.replaceWith(
			this.buttonItem = this._renderButtonItem(

				// Fall back to an empty object in case there are no options
				this._getSelectedItem().data( "ui-selectmenu-item" ) || {}
			)
		);
		if ( this.options.width === null ) {
			this._resizeButton();
		}
	},

	_refreshMenu: function() {
		var item,
			options = this.element.find( "option" );

		this.menu.empty();

		this._parseOptions( options );
		this._renderMenu( this.menu, this.items );

		this.menuInstance.refresh();
		this.menuItems = this.menu.find( "li" )
			.not( ".ui-selectmenu-optgroup" )
				.find( ".ui-menu-item-wrapper" );

		this._rendered = true;

		if ( !options.length ) {
			return;
		}

		item = this._getSelectedItem();

		// Update the menu to have the correct item focused
		this.menuInstance.focus( null, item );
		this._setAria( item.data( "ui-selectmenu-item" ) );

		// Set disabled state
		this._setOption( "disabled", this.element.prop( "disabled" ) );
	},

	open: function( event ) {
		if ( this.options.disabled ) {
			return;
		}

		// If this is the first time the menu is being opened, render the items
		if ( !this._rendered ) {
			this._refreshMenu();
		} else {

			// Menu clears focus on close, reset focus to selected item
			this._removeClass( this.menu.find( ".ui-state-active" ), null, "ui-state-active" );
			this.menuInstance.focus( null, this._getSelectedItem() );
		}

		// If there are no options, don't open the menu
		if ( !this.menuItems.length ) {
			return;
		}

		this.isOpen = true;
		this._toggleAttr();
		this._resizeMenu();
		this._position();

		this._on( this.document, this._documentClick );

		this._trigger( "open", event );
	},

	_position: function() {
		this.menuWrap.position( $.extend( { of: this.button }, this.options.position ) );
	},

	close: function( event ) {
		if ( !this.isOpen ) {
			return;
		}

		this.isOpen = false;
		this._toggleAttr();

		this.range = null;
		this._off( this.document );

		this._trigger( "close", event );
	},

	widget: function() {
		return this.button;
	},

	menuWidget: function() {
		return this.menu;
	},

	_renderButtonItem: function( item ) {
		var buttonItem = $( "<span>" );

		this._setText( buttonItem, item.label );
		this._addClass( buttonItem, "ui-selectmenu-text" );

		return buttonItem;
	},

	_renderMenu: function( ul, items ) {
		var that = this,
			currentOptgroup = "";

		$.each( items, function( index, item ) {
			var li;

			if ( item.optgroup !== currentOptgroup ) {
				li = $( "<li>", {
					text: item.optgroup
				} );
				that._addClass( li, "ui-selectmenu-optgroup", "ui-menu-divider" +
					( item.element.parent( "optgroup" ).prop( "disabled" ) ?
						" ui-state-disabled" :
						"" ) );

				li.appendTo( ul );

				currentOptgroup = item.optgroup;
			}

			that._renderItemData( ul, item );
		} );
	},

	_renderItemData: function( ul, item ) {
		return this._renderItem( ul, item ).data( "ui-selectmenu-item", item );
	},

	_renderItem: function( ul, item ) {
		var li = $( "<li>" ),
			wrapper = $( "<div>", {
				title: item.element.attr( "title" )
			} );

		if ( item.disabled ) {
			this._addClass( li, null, "ui-state-disabled" );
		}
		this._setText( wrapper, item.label );

		return li.append( wrapper ).appendTo( ul );
	},

	_setText: function( element, value ) {
		if ( value ) {
			element.text( value );
		} else {
			element.html( "&#160;" );
		}
	},

	_move: function( direction, event ) {
		var item, next,
			filter = ".ui-menu-item";

		if ( this.isOpen ) {
			item = this.menuItems.eq( this.focusIndex ).parent( "li" );
		} else {
			item = this.menuItems.eq( this.element[ 0 ].selectedIndex ).parent( "li" );
			filter += ":not(.ui-state-disabled)";
		}

		if ( direction === "first" || direction === "last" ) {
			next = item[ direction === "first" ? "prevAll" : "nextAll" ]( filter ).eq( -1 );
		} else {
			next = item[ direction + "All" ]( filter ).eq( 0 );
		}

		if ( next.length ) {
			this.menuInstance.focus( event, next );
		}
	},

	_getSelectedItem: function() {
		return this.menuItems.eq( this.element[ 0 ].selectedIndex ).parent( "li" );
	},

	_toggle: function( event ) {
		this[ this.isOpen ? "close" : "open" ]( event );
	},

	_setSelection: function() {
		var selection;

		if ( !this.range ) {
			return;
		}

		if ( window.getSelection ) {
			selection = window.getSelection();
			selection.removeAllRanges();
			selection.addRange( this.range );

		// Support: IE8
		} else {
			this.range.select();
		}

		// Support: IE
		// Setting the text selection kills the button focus in IE, but
		// restoring the focus doesn't kill the selection.
		this.button.focus();
	},

	_documentClick: {
		mousedown: function( event ) {
			if ( !this.isOpen ) {
				return;
			}

			if ( !$( event.target ).closest( ".ui-selectmenu-menu, #" +
					$.ui.escapeSelector( this.ids.button ) ).length ) {
				this.close( event );
			}
		}
	},

	_buttonEvents: {

		// Prevent text selection from being reset when interacting with the selectmenu (#10144)
		mousedown: function() {
			var selection;

			if ( window.getSelection ) {
				selection = window.getSelection();
				if ( selection.rangeCount ) {
					this.range = selection.getRangeAt( 0 );
				}

			// Support: IE8
			} else {
				this.range = document.selection.createRange();
			}
		},

		click: function( event ) {
			this._setSelection();
			this._toggle( event );
		},

		keydown: function( event ) {
			var preventDefault = true;
			switch ( event.keyCode ) {
			case $.ui.keyCode.TAB:
			case $.ui.keyCode.ESCAPE:
				this.close( event );
				preventDefault = false;
				break;
			case $.ui.keyCode.ENTER:
				if ( this.isOpen ) {
					this._selectFocusedItem( event );
				}
				break;
			case $.ui.keyCode.UP:
				if ( event.altKey ) {
					this._toggle( event );
				} else {
					this._move( "prev", event );
				}
				break;
			case $.ui.keyCode.DOWN:
				if ( event.altKey ) {
					this._toggle( event );
				} else {
					this._move( "next", event );
				}
				break;
			case $.ui.keyCode.SPACE:
				if ( this.isOpen ) {
					this._selectFocusedItem( event );
				} else {
					this._toggle( event );
				}
				break;
			case $.ui.keyCode.LEFT:
				this._move( "prev", event );
				break;
			case $.ui.keyCode.RIGHT:
				this._move( "next", event );
				break;
			case $.ui.keyCode.HOME:
			case $.ui.keyCode.PAGE_UP:
				this._move( "first", event );
				break;
			case $.ui.keyCode.END:
			case $.ui.keyCode.PAGE_DOWN:
				this._move( "last", event );
				break;
			default:
				this.menu.trigger( event );
				preventDefault = false;
			}

			if ( preventDefault ) {
				event.preventDefault();
			}
		}
	},

	_selectFocusedItem: function( event ) {
		var item = this.menuItems.eq( this.focusIndex ).parent( "li" );
		if ( !item.hasClass( "ui-state-disabled" ) ) {
			this._select( item.data( "ui-selectmenu-item" ), event );
		}
	},

	_select: function( item, event ) {
		var oldIndex = this.element[ 0 ].selectedIndex;

		// Change native select element
		this.element[ 0 ].selectedIndex = item.index;
		this.buttonItem.replaceWith( this.buttonItem = this._renderButtonItem( item ) );
		this._setAria( item );
		this._trigger( "select", event, { item: item } );

		if ( item.index !== oldIndex ) {
			this._trigger( "change", event, { item: item } );
		}

		this.close( event );
	},

	_setAria: function( item ) {
		var id = this.menuItems.eq( item.index ).attr( "id" );

		this.button.attr( {
			"aria-labelledby": id,
			"aria-activedescendant": id
		} );
		this.menu.attr( "aria-activedescendant", id );
	},

	_setOption: function( key, value ) {
		if ( key === "icons" ) {
			var icon = this.button.find( "span.ui-icon" );
			this._removeClass( icon, null, this.options.icons.button )
				._addClass( icon, null, value.button );
		}

		this._super( key, value );

		if ( key === "appendTo" ) {
			this.menuWrap.appendTo( this._appendTo() );
		}

		if ( key === "width" ) {
			this._resizeButton();
		}
	},

	_setOptionDisabled: function( value ) {
		this._super( value );

		this.menuInstance.option( "disabled", value );
		this.button.attr( "aria-disabled", value );
		this._toggleClass( this.button, null, "ui-state-disabled", value );

		this.element.prop( "disabled", value );
		if ( value ) {
			this.button.attr( "tabindex", -1 );
			this.close();
		} else {
			this.button.attr( "tabindex", 0 );
		}
	},

	_appendTo: function() {
		var element = this.options.appendTo;

		if ( element ) {
			element = element.jquery || element.nodeType ?
				$( element ) :
				this.document.find( element ).eq( 0 );
		}

		if ( !element || !element[ 0 ] ) {
			element = this.element.closest( ".ui-front, dialog" );
		}

		if ( !element.length ) {
			element = this.document[ 0 ].body;
		}

		return element;
	},

	_toggleAttr: function() {
		this.button.attr( "aria-expanded", this.isOpen );

		// We can't use two _toggleClass() calls here, because we need to make sure
		// we always remove classes first and add them second, otherwise if both classes have the
		// same theme class, it will be removed after we add it.
		this._removeClass( this.button, "ui-selectmenu-button-" +
			( this.isOpen ? "closed" : "open" ) )
			._addClass( this.button, "ui-selectmenu-button-" +
				( this.isOpen ? "open" : "closed" ) )
			._toggleClass( this.menuWrap, "ui-selectmenu-open", null, this.isOpen );

		this.menu.attr( "aria-hidden", !this.isOpen );
	},

	_resizeButton: function() {
		var width = this.options.width;

		// For `width: false`, just remove inline style and stop
		if ( width === false ) {
			this.button.css( "width", "" );
			return;
		}

		// For `width: null`, match the width of the original element
		if ( width === null ) {
			width = this.element.show().outerWidth();
			this.element.hide();
		}

		this.button.outerWidth( width );
	},

	_resizeMenu: function() {
		this.menu.outerWidth( Math.max(
			this.button.outerWidth(),

			// Support: IE10
			// IE10 wraps long text (possibly a rounding bug)
			// so we add 1px to avoid the wrapping
			this.menu.width( "" ).outerWidth() + 1
		) );
	},

	_getCreateOptions: function() {
		var options = this._super();

		options.disabled = this.element.prop( "disabled" );

		return options;
	},

	_parseOptions: function( options ) {
		var that = this,
			data = [];
		options.each( function( index, item ) {
			data.push( that._parseOption( $( item ), index ) );
		} );
		this.items = data;
	},

	_parseOption: function( option, index ) {
		var optgroup = option.parent( "optgroup" );

		return {
			element: option,
			index: index,
			value: option.val(),
			label: option.text(),
			optgroup: optgroup.attr( "label" ) || "",
			disabled: optgroup.prop( "disabled" ) || option.prop( "disabled" )
		};
	},

	_destroy: function() {
		this._unbindFormResetHandler();
		this.menuWrap.remove();
		this.button.remove();
		this.element.show();
		this.element.removeUniqueId();
		this.labels.attr( "for", this.ids.element );
	}
} ] );


/*!
 * jQuery UI Slider 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Slider
//>>group: Widgets
//>>description: Displays a flexible slider with ranges and accessibility via keyboard.
//>>docs: http://api.jqueryui.com/slider/
//>>demos: http://jqueryui.com/slider/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/slider.css
//>>css.theme: ../../themes/base/theme.css



var widgetsSlider = $.widget( "ui.slider", $.ui.mouse, {
	version: "1.12.1",
	widgetEventPrefix: "slide",

	options: {
		animate: false,
		classes: {
			"ui-slider": "ui-corner-all",
			"ui-slider-handle": "ui-corner-all",

			// Note: ui-widget-header isn't the most fittingly semantic framework class for this
			// element, but worked best visually with a variety of themes
			"ui-slider-range": "ui-corner-all ui-widget-header"
		},
		distance: 0,
		max: 100,
		min: 0,
		orientation: "horizontal",
		range: false,
		step: 1,
		value: 0,
		values: null,

		// Callbacks
		change: null,
		slide: null,
		start: null,
		stop: null
	},

	// Number of pages in a slider
	// (how many times can you page up/down to go through the whole range)
	numPages: 5,

	_create: function() {
		this._keySliding = false;
		this._mouseSliding = false;
		this._animateOff = true;
		this._handleIndex = null;
		this._detectOrientation();
		this._mouseInit();
		this._calculateNewMax();

		this._addClass( "ui-slider ui-slider-" + this.orientation,
			"ui-widget ui-widget-content" );

		this._refresh();

		this._animateOff = false;
	},

	_refresh: function() {
		this._createRange();
		this._createHandles();
		this._setupEvents();
		this._refreshValue();
	},

	_createHandles: function() {
		var i, handleCount,
			options = this.options,
			existingHandles = this.element.find( ".ui-slider-handle" ),
			handle = "<span tabindex='0'></span>",
			handles = [];

		handleCount = ( options.values && options.values.length ) || 1;

		if ( existingHandles.length > handleCount ) {
			existingHandles.slice( handleCount ).remove();
			existingHandles = existingHandles.slice( 0, handleCount );
		}

		for ( i = existingHandles.length; i < handleCount; i++ ) {
			handles.push( handle );
		}

		this.handles = existingHandles.add( $( handles.join( "" ) ).appendTo( this.element ) );

		this._addClass( this.handles, "ui-slider-handle", "ui-state-default" );

		this.handle = this.handles.eq( 0 );

		this.handles.each( function( i ) {
			$( this )
				.data( "ui-slider-handle-index", i )
				.attr( "tabIndex", 0 );
		} );
	},

	_createRange: function() {
		var options = this.options;

		if ( options.range ) {
			if ( options.range === true ) {
				if ( !options.values ) {
					options.values = [ this._valueMin(), this._valueMin() ];
				} else if ( options.values.length && options.values.length !== 2 ) {
					options.values = [ options.values[ 0 ], options.values[ 0 ] ];
				} else if ( $.isArray( options.values ) ) {
					options.values = options.values.slice( 0 );
				}
			}

			if ( !this.range || !this.range.length ) {
				this.range = $( "<div>" )
					.appendTo( this.element );

				this._addClass( this.range, "ui-slider-range" );
			} else {
				this._removeClass( this.range, "ui-slider-range-min ui-slider-range-max" );

				// Handle range switching from true to min/max
				this.range.css( {
					"left": "",
					"bottom": ""
				} );
			}
			if ( options.range === "min" || options.range === "max" ) {
				this._addClass( this.range, "ui-slider-range-" + options.range );
			}
		} else {
			if ( this.range ) {
				this.range.remove();
			}
			this.range = null;
		}
	},

	_setupEvents: function() {
		this._off( this.handles );
		this._on( this.handles, this._handleEvents );
		this._hoverable( this.handles );
		this._focusable( this.handles );
	},

	_destroy: function() {
		this.handles.remove();
		if ( this.range ) {
			this.range.remove();
		}

		this._mouseDestroy();
	},

	_mouseCapture: function( event ) {
		var position, normValue, distance, closestHandle, index, allowed, offset, mouseOverHandle,
			that = this,
			o = this.options;

		if ( o.disabled ) {
			return false;
		}

		this.elementSize = {
			width: this.element.outerWidth(),
			height: this.element.outerHeight()
		};
		this.elementOffset = this.element.offset();

		position = { x: event.pageX, y: event.pageY };
		normValue = this._normValueFromMouse( position );
		distance = this._valueMax() - this._valueMin() + 1;
		this.handles.each( function( i ) {
			var thisDistance = Math.abs( normValue - that.values( i ) );
			if ( ( distance > thisDistance ) ||
				( distance === thisDistance &&
					( i === that._lastChangedValue || that.values( i ) === o.min ) ) ) {
				distance = thisDistance;
				closestHandle = $( this );
				index = i;
			}
		} );

		allowed = this._start( event, index );
		if ( allowed === false ) {
			return false;
		}
		this._mouseSliding = true;

		this._handleIndex = index;

		this._addClass( closestHandle, null, "ui-state-active" );
		closestHandle.trigger( "focus" );

		offset = closestHandle.offset();
		mouseOverHandle = !$( event.target ).parents().addBack().is( ".ui-slider-handle" );
		this._clickOffset = mouseOverHandle ? { left: 0, top: 0 } : {
			left: event.pageX - offset.left - ( closestHandle.width() / 2 ),
			top: event.pageY - offset.top -
				( closestHandle.height() / 2 ) -
				( parseInt( closestHandle.css( "borderTopWidth" ), 10 ) || 0 ) -
				( parseInt( closestHandle.css( "borderBottomWidth" ), 10 ) || 0 ) +
				( parseInt( closestHandle.css( "marginTop" ), 10 ) || 0 )
		};

		if ( !this.handles.hasClass( "ui-state-hover" ) ) {
			this._slide( event, index, normValue );
		}
		this._animateOff = true;
		return true;
	},

	_mouseStart: function() {
		return true;
	},

	_mouseDrag: function( event ) {
		var position = { x: event.pageX, y: event.pageY },
			normValue = this._normValueFromMouse( position );

		this._slide( event, this._handleIndex, normValue );

		return false;
	},

	_mouseStop: function( event ) {
		this._removeClass( this.handles, null, "ui-state-active" );
		this._mouseSliding = false;

		this._stop( event, this._handleIndex );
		this._change( event, this._handleIndex );

		this._handleIndex = null;
		this._clickOffset = null;
		this._animateOff = false;

		return false;
	},

	_detectOrientation: function() {
		this.orientation = ( this.options.orientation === "vertical" ) ? "vertical" : "horizontal";
	},

	_normValueFromMouse: function( position ) {
		var pixelTotal,
			pixelMouse,
			percentMouse,
			valueTotal,
			valueMouse;

		if ( this.orientation === "horizontal" ) {
			pixelTotal = this.elementSize.width;
			pixelMouse = position.x - this.elementOffset.left -
				( this._clickOffset ? this._clickOffset.left : 0 );
		} else {
			pixelTotal = this.elementSize.height;
			pixelMouse = position.y - this.elementOffset.top -
				( this._clickOffset ? this._clickOffset.top : 0 );
		}

		percentMouse = ( pixelMouse / pixelTotal );
		if ( percentMouse > 1 ) {
			percentMouse = 1;
		}
		if ( percentMouse < 0 ) {
			percentMouse = 0;
		}
		if ( this.orientation === "vertical" ) {
			percentMouse = 1 - percentMouse;
		}

		valueTotal = this._valueMax() - this._valueMin();
		valueMouse = this._valueMin() + percentMouse * valueTotal;

		return this._trimAlignValue( valueMouse );
	},

	_uiHash: function( index, value, values ) {
		var uiHash = {
			handle: this.handles[ index ],
			handleIndex: index,
			value: value !== undefined ? value : this.value()
		};

		if ( this._hasMultipleValues() ) {
			uiHash.value = value !== undefined ? value : this.values( index );
			uiHash.values = values || this.values();
		}

		return uiHash;
	},

	_hasMultipleValues: function() {
		return this.options.values && this.options.values.length;
	},

	_start: function( event, index ) {
		return this._trigger( "start", event, this._uiHash( index ) );
	},

	_slide: function( event, index, newVal ) {
		var allowed, otherVal,
			currentValue = this.value(),
			newValues = this.values();

		if ( this._hasMultipleValues() ) {
			otherVal = this.values( index ? 0 : 1 );
			currentValue = this.values( index );

			if ( this.options.values.length === 2 && this.options.range === true ) {
				newVal =  index === 0 ? Math.min( otherVal, newVal ) : Math.max( otherVal, newVal );
			}

			newValues[ index ] = newVal;
		}

		if ( newVal === currentValue ) {
			return;
		}

		allowed = this._trigger( "slide", event, this._uiHash( index, newVal, newValues ) );

		// A slide can be canceled by returning false from the slide callback
		if ( allowed === false ) {
			return;
		}

		if ( this._hasMultipleValues() ) {
			this.values( index, newVal );
		} else {
			this.value( newVal );
		}
	},

	_stop: function( event, index ) {
		this._trigger( "stop", event, this._uiHash( index ) );
	},

	_change: function( event, index ) {
		if ( !this._keySliding && !this._mouseSliding ) {

			//store the last changed value index for reference when handles overlap
			this._lastChangedValue = index;
			this._trigger( "change", event, this._uiHash( index ) );
		}
	},

	value: function( newValue ) {
		if ( arguments.length ) {
			this.options.value = this._trimAlignValue( newValue );
			this._refreshValue();
			this._change( null, 0 );
			return;
		}

		return this._value();
	},

	values: function( index, newValue ) {
		var vals,
			newValues,
			i;

		if ( arguments.length > 1 ) {
			this.options.values[ index ] = this._trimAlignValue( newValue );
			this._refreshValue();
			this._change( null, index );
			return;
		}

		if ( arguments.length ) {
			if ( $.isArray( arguments[ 0 ] ) ) {
				vals = this.options.values;
				newValues = arguments[ 0 ];
				for ( i = 0; i < vals.length; i += 1 ) {
					vals[ i ] = this._trimAlignValue( newValues[ i ] );
					this._change( null, i );
				}
				this._refreshValue();
			} else {
				if ( this._hasMultipleValues() ) {
					return this._values( index );
				} else {
					return this.value();
				}
			}
		} else {
			return this._values();
		}
	},

	_setOption: function( key, value ) {
		var i,
			valsLength = 0;

		if ( key === "range" && this.options.range === true ) {
			if ( value === "min" ) {
				this.options.value = this._values( 0 );
				this.options.values = null;
			} else if ( value === "max" ) {
				this.options.value = this._values( this.options.values.length - 1 );
				this.options.values = null;
			}
		}

		if ( $.isArray( this.options.values ) ) {
			valsLength = this.options.values.length;
		}

		this._super( key, value );

		switch ( key ) {
			case "orientation":
				this._detectOrientation();
				this._removeClass( "ui-slider-horizontal ui-slider-vertical" )
					._addClass( "ui-slider-" + this.orientation );
				this._refreshValue();
				if ( this.options.range ) {
					this._refreshRange( value );
				}

				// Reset positioning from previous orientation
				this.handles.css( value === "horizontal" ? "bottom" : "left", "" );
				break;
			case "value":
				this._animateOff = true;
				this._refreshValue();
				this._change( null, 0 );
				this._animateOff = false;
				break;
			case "values":
				this._animateOff = true;
				this._refreshValue();

				// Start from the last handle to prevent unreachable handles (#9046)
				for ( i = valsLength - 1; i >= 0; i-- ) {
					this._change( null, i );
				}
				this._animateOff = false;
				break;
			case "step":
			case "min":
			case "max":
				this._animateOff = true;
				this._calculateNewMax();
				this._refreshValue();
				this._animateOff = false;
				break;
			case "range":
				this._animateOff = true;
				this._refresh();
				this._animateOff = false;
				break;
		}
	},

	_setOptionDisabled: function( value ) {
		this._super( value );

		this._toggleClass( null, "ui-state-disabled", !!value );
	},

	//internal value getter
	// _value() returns value trimmed by min and max, aligned by step
	_value: function() {
		var val = this.options.value;
		val = this._trimAlignValue( val );

		return val;
	},

	//internal values getter
	// _values() returns array of values trimmed by min and max, aligned by step
	// _values( index ) returns single value trimmed by min and max, aligned by step
	_values: function( index ) {
		var val,
			vals,
			i;

		if ( arguments.length ) {
			val = this.options.values[ index ];
			val = this._trimAlignValue( val );

			return val;
		} else if ( this._hasMultipleValues() ) {

			// .slice() creates a copy of the array
			// this copy gets trimmed by min and max and then returned
			vals = this.options.values.slice();
			for ( i = 0; i < vals.length; i += 1 ) {
				vals[ i ] = this._trimAlignValue( vals[ i ] );
			}

			return vals;
		} else {
			return [];
		}
	},

	// Returns the step-aligned value that val is closest to, between (inclusive) min and max
	_trimAlignValue: function( val ) {
		if ( val <= this._valueMin() ) {
			return this._valueMin();
		}
		if ( val >= this._valueMax() ) {
			return this._valueMax();
		}
		var step = ( this.options.step > 0 ) ? this.options.step : 1,
			valModStep = ( val - this._valueMin() ) % step,
			alignValue = val - valModStep;

		if ( Math.abs( valModStep ) * 2 >= step ) {
			alignValue += ( valModStep > 0 ) ? step : ( -step );
		}

		// Since JavaScript has problems with large floats, round
		// the final value to 5 digits after the decimal point (see #4124)
		return parseFloat( alignValue.toFixed( 5 ) );
	},

	_calculateNewMax: function() {
		var max = this.options.max,
			min = this._valueMin(),
			step = this.options.step,
			aboveMin = Math.round( ( max - min ) / step ) * step;
		max = aboveMin + min;
		if ( max > this.options.max ) {

			//If max is not divisible by step, rounding off may increase its value
			max -= step;
		}
		this.max = parseFloat( max.toFixed( this._precision() ) );
	},

	_precision: function() {
		var precision = this._precisionOf( this.options.step );
		if ( this.options.min !== null ) {
			precision = Math.max( precision, this._precisionOf( this.options.min ) );
		}
		return precision;
	},

	_precisionOf: function( num ) {
		var str = num.toString(),
			decimal = str.indexOf( "." );
		return decimal === -1 ? 0 : str.length - decimal - 1;
	},

	_valueMin: function() {
		return this.options.min;
	},

	_valueMax: function() {
		return this.max;
	},

	_refreshRange: function( orientation ) {
		if ( orientation === "vertical" ) {
			this.range.css( { "width": "", "left": "" } );
		}
		if ( orientation === "horizontal" ) {
			this.range.css( { "height": "", "bottom": "" } );
		}
	},

	_refreshValue: function() {
		var lastValPercent, valPercent, value, valueMin, valueMax,
			oRange = this.options.range,
			o = this.options,
			that = this,
			animate = ( !this._animateOff ) ? o.animate : false,
			_set = {};

		if ( this._hasMultipleValues() ) {
			this.handles.each( function( i ) {
				valPercent = ( that.values( i ) - that._valueMin() ) / ( that._valueMax() -
					that._valueMin() ) * 100;
				_set[ that.orientation === "horizontal" ? "left" : "bottom" ] = valPercent + "%";
				$( this ).stop( 1, 1 )[ animate ? "animate" : "css" ]( _set, o.animate );
				if ( that.options.range === true ) {
					if ( that.orientation === "horizontal" ) {
						if ( i === 0 ) {
							that.range.stop( 1, 1 )[ animate ? "animate" : "css" ]( {
								left: valPercent + "%"
							}, o.animate );
						}
						if ( i === 1 ) {
							that.range[ animate ? "animate" : "css" ]( {
								width: ( valPercent - lastValPercent ) + "%"
							}, {
								queue: false,
								duration: o.animate
							} );
						}
					} else {
						if ( i === 0 ) {
							that.range.stop( 1, 1 )[ animate ? "animate" : "css" ]( {
								bottom: ( valPercent ) + "%"
							}, o.animate );
						}
						if ( i === 1 ) {
							that.range[ animate ? "animate" : "css" ]( {
								height: ( valPercent - lastValPercent ) + "%"
							}, {
								queue: false,
								duration: o.animate
							} );
						}
					}
				}
				lastValPercent = valPercent;
			} );
		} else {
			value = this.value();
			valueMin = this._valueMin();
			valueMax = this._valueMax();
			valPercent = ( valueMax !== valueMin ) ?
					( value - valueMin ) / ( valueMax - valueMin ) * 100 :
					0;
			_set[ this.orientation === "horizontal" ? "left" : "bottom" ] = valPercent + "%";
			this.handle.stop( 1, 1 )[ animate ? "animate" : "css" ]( _set, o.animate );

			if ( oRange === "min" && this.orientation === "horizontal" ) {
				this.range.stop( 1, 1 )[ animate ? "animate" : "css" ]( {
					width: valPercent + "%"
				}, o.animate );
			}
			if ( oRange === "max" && this.orientation === "horizontal" ) {
				this.range.stop( 1, 1 )[ animate ? "animate" : "css" ]( {
					width: ( 100 - valPercent ) + "%"
				}, o.animate );
			}
			if ( oRange === "min" && this.orientation === "vertical" ) {
				this.range.stop( 1, 1 )[ animate ? "animate" : "css" ]( {
					height: valPercent + "%"
				}, o.animate );
			}
			if ( oRange === "max" && this.orientation === "vertical" ) {
				this.range.stop( 1, 1 )[ animate ? "animate" : "css" ]( {
					height: ( 100 - valPercent ) + "%"
				}, o.animate );
			}
		}
	},

	_handleEvents: {
		keydown: function( event ) {
			var allowed, curVal, newVal, step,
				index = $( event.target ).data( "ui-slider-handle-index" );

			switch ( event.keyCode ) {
				case $.ui.keyCode.HOME:
				case $.ui.keyCode.END:
				case $.ui.keyCode.PAGE_UP:
				case $.ui.keyCode.PAGE_DOWN:
				case $.ui.keyCode.UP:
				case $.ui.keyCode.RIGHT:
				case $.ui.keyCode.DOWN:
				case $.ui.keyCode.LEFT:
					event.preventDefault();
					if ( !this._keySliding ) {
						this._keySliding = true;
						this._addClass( $( event.target ), null, "ui-state-active" );
						allowed = this._start( event, index );
						if ( allowed === false ) {
							return;
						}
					}
					break;
			}

			step = this.options.step;
			if ( this._hasMultipleValues() ) {
				curVal = newVal = this.values( index );
			} else {
				curVal = newVal = this.value();
			}

			switch ( event.keyCode ) {
				case $.ui.keyCode.HOME:
					newVal = this._valueMin();
					break;
				case $.ui.keyCode.END:
					newVal = this._valueMax();
					break;
				case $.ui.keyCode.PAGE_UP:
					newVal = this._trimAlignValue(
						curVal + ( ( this._valueMax() - this._valueMin() ) / this.numPages )
					);
					break;
				case $.ui.keyCode.PAGE_DOWN:
					newVal = this._trimAlignValue(
						curVal - ( ( this._valueMax() - this._valueMin() ) / this.numPages ) );
					break;
				case $.ui.keyCode.UP:
				case $.ui.keyCode.RIGHT:
					if ( curVal === this._valueMax() ) {
						return;
					}
					newVal = this._trimAlignValue( curVal + step );
					break;
				case $.ui.keyCode.DOWN:
				case $.ui.keyCode.LEFT:
					if ( curVal === this._valueMin() ) {
						return;
					}
					newVal = this._trimAlignValue( curVal - step );
					break;
			}

			this._slide( event, index, newVal );
		},
		keyup: function( event ) {
			var index = $( event.target ).data( "ui-slider-handle-index" );

			if ( this._keySliding ) {
				this._keySliding = false;
				this._stop( event, index );
				this._change( event, index );
				this._removeClass( $( event.target ), null, "ui-state-active" );
			}
		}
	}
} );


/*!
 * jQuery UI Sortable 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Sortable
//>>group: Interactions
//>>description: Enables items in a list to be sorted using the mouse.
//>>docs: http://api.jqueryui.com/sortable/
//>>demos: http://jqueryui.com/sortable/
//>>css.structure: ../../themes/base/sortable.css



var widgetsSortable = $.widget( "ui.sortable", $.ui.mouse, {
	version: "1.12.1",
	widgetEventPrefix: "sort",
	ready: false,
	options: {
		appendTo: "parent",
		axis: false,
		connectWith: false,
		containment: false,
		cursor: "auto",
		cursorAt: false,
		dropOnEmpty: true,
		forcePlaceholderSize: false,
		forceHelperSize: false,
		grid: false,
		handle: false,
		helper: "original",
		items: "> *",
		opacity: false,
		placeholder: false,
		revert: false,
		scroll: true,
		scrollSensitivity: 20,
		scrollSpeed: 20,
		scope: "default",
		tolerance: "intersect",
		zIndex: 1000,

		// Callbacks
		activate: null,
		beforeStop: null,
		change: null,
		deactivate: null,
		out: null,
		over: null,
		receive: null,
		remove: null,
		sort: null,
		start: null,
		stop: null,
		update: null
	},

	_isOverAxis: function( x, reference, size ) {
		return ( x >= reference ) && ( x < ( reference + size ) );
	},

	_isFloating: function( item ) {
		return ( /left|right/ ).test( item.css( "float" ) ) ||
			( /inline|table-cell/ ).test( item.css( "display" ) );
	},

	_create: function() {
		this.containerCache = {};
		this._addClass( "ui-sortable" );

		//Get the items
		this.refresh();

		//Let's determine the parent's offset
		this.offset = this.element.offset();

		//Initialize mouse events for interaction
		this._mouseInit();

		this._setHandleClassName();

		//We're ready to go
		this.ready = true;

	},

	_setOption: function( key, value ) {
		this._super( key, value );

		if ( key === "handle" ) {
			this._setHandleClassName();
		}
	},

	_setHandleClassName: function() {
		var that = this;
		this._removeClass( this.element.find( ".ui-sortable-handle" ), "ui-sortable-handle" );
		$.each( this.items, function() {
			that._addClass(
				this.instance.options.handle ?
					this.item.find( this.instance.options.handle ) :
					this.item,
				"ui-sortable-handle"
			);
		} );
	},

	_destroy: function() {
		this._mouseDestroy();

		for ( var i = this.items.length - 1; i >= 0; i-- ) {
			this.items[ i ].item.removeData( this.widgetName + "-item" );
		}

		return this;
	},

	_mouseCapture: function( event, overrideHandle ) {
		var currentItem = null,
			validHandle = false,
			that = this;

		if ( this.reverting ) {
			return false;
		}

		if ( this.options.disabled || this.options.type === "static" ) {
			return false;
		}

		//We have to refresh the items data once first
		this._refreshItems( event );

		//Find out if the clicked node (or one of its parents) is a actual item in this.items
		$( event.target ).parents().each( function() {
			if ( $.data( this, that.widgetName + "-item" ) === that ) {
				currentItem = $( this );
				return false;
			}
		} );
		if ( $.data( event.target, that.widgetName + "-item" ) === that ) {
			currentItem = $( event.target );
		}

		if ( !currentItem ) {
			return false;
		}
		if ( this.options.handle && !overrideHandle ) {
			$( this.options.handle, currentItem ).find( "*" ).addBack().each( function() {
				if ( this === event.target ) {
					validHandle = true;
				}
			} );
			if ( !validHandle ) {
				return false;
			}
		}

		this.currentItem = currentItem;
		this._removeCurrentsFromItems();
		return true;

	},

	_mouseStart: function( event, overrideHandle, noActivation ) {

		var i, body,
			o = this.options;

		this.currentContainer = this;

		//We only need to call refreshPositions, because the refreshItems call has been moved to
		// mouseCapture
		this.refreshPositions();

		//Create and append the visible helper
		this.helper = this._createHelper( event );

		//Cache the helper size
		this._cacheHelperProportions();

		/*
		 * - Position generation -
		 * This block generates everything position related - it's the core of draggables.
		 */

		//Cache the margins of the original element
		this._cacheMargins();

		//Get the next scrolling parent
		this.scrollParent = this.helper.scrollParent();

		//The element's absolute position on the page minus margins
		this.offset = this.currentItem.offset();
		this.offset = {
			top: this.offset.top - this.margins.top,
			left: this.offset.left - this.margins.left
		};

		$.extend( this.offset, {
			click: { //Where the click happened, relative to the element
				left: event.pageX - this.offset.left,
				top: event.pageY - this.offset.top
			},
			parent: this._getParentOffset(),

			// This is a relative to absolute position minus the actual position calculation -
			// only used for relative positioned helper
			relative: this._getRelativeOffset()
		} );

		// Only after we got the offset, we can change the helper's position to absolute
		// TODO: Still need to figure out a way to make relative sorting possible
		this.helper.css( "position", "absolute" );
		this.cssPosition = this.helper.css( "position" );

		//Generate the original position
		this.originalPosition = this._generatePosition( event );
		this.originalPageX = event.pageX;
		this.originalPageY = event.pageY;

		//Adjust the mouse offset relative to the helper if "cursorAt" is supplied
		( o.cursorAt && this._adjustOffsetFromHelper( o.cursorAt ) );

		//Cache the former DOM position
		this.domPosition = {
			prev: this.currentItem.prev()[ 0 ],
			parent: this.currentItem.parent()[ 0 ]
		};

		// If the helper is not the original, hide the original so it's not playing any role during
		// the drag, won't cause anything bad this way
		if ( this.helper[ 0 ] !== this.currentItem[ 0 ] ) {
			this.currentItem.hide();
		}

		//Create the placeholder
		this._createPlaceholder();

		//Set a containment if given in the options
		if ( o.containment ) {
			this._setContainment();
		}

		if ( o.cursor && o.cursor !== "auto" ) { // cursor option
			body = this.document.find( "body" );

			// Support: IE
			this.storedCursor = body.css( "cursor" );
			body.css( "cursor", o.cursor );

			this.storedStylesheet =
				$( "<style>*{ cursor: " + o.cursor + " !important; }</style>" ).appendTo( body );
		}

		if ( o.opacity ) { // opacity option
			if ( this.helper.css( "opacity" ) ) {
				this._storedOpacity = this.helper.css( "opacity" );
			}
			this.helper.css( "opacity", o.opacity );
		}

		if ( o.zIndex ) { // zIndex option
			if ( this.helper.css( "zIndex" ) ) {
				this._storedZIndex = this.helper.css( "zIndex" );
			}
			this.helper.css( "zIndex", o.zIndex );
		}

		//Prepare scrolling
		if ( this.scrollParent[ 0 ] !== this.document[ 0 ] &&
				this.scrollParent[ 0 ].tagName !== "HTML" ) {
			this.overflowOffset = this.scrollParent.offset();
		}

		//Call callbacks
		this._trigger( "start", event, this._uiHash() );

		//Recache the helper size
		if ( !this._preserveHelperProportions ) {
			this._cacheHelperProportions();
		}

		//Post "activate" events to possible containers
		if ( !noActivation ) {
			for ( i = this.containers.length - 1; i >= 0; i-- ) {
				this.containers[ i ]._trigger( "activate", event, this._uiHash( this ) );
			}
		}

		//Prepare possible droppables
		if ( $.ui.ddmanager ) {
			$.ui.ddmanager.current = this;
		}

		if ( $.ui.ddmanager && !o.dropBehaviour ) {
			$.ui.ddmanager.prepareOffsets( this, event );
		}

		this.dragging = true;

		this._addClass( this.helper, "ui-sortable-helper" );

		// Execute the drag once - this causes the helper not to be visiblebefore getting its
		// correct position
		this._mouseDrag( event );
		return true;

	},

	_mouseDrag: function( event ) {
		var i, item, itemElement, intersection,
			o = this.options,
			scrolled = false;

		//Compute the helpers position
		this.position = this._generatePosition( event );
		this.positionAbs = this._convertPositionTo( "absolute" );

		if ( !this.lastPositionAbs ) {
			this.lastPositionAbs = this.positionAbs;
		}

		//Do scrolling
		if ( this.options.scroll ) {
			if ( this.scrollParent[ 0 ] !== this.document[ 0 ] &&
					this.scrollParent[ 0 ].tagName !== "HTML" ) {

				if ( ( this.overflowOffset.top + this.scrollParent[ 0 ].offsetHeight ) -
						event.pageY < o.scrollSensitivity ) {
					this.scrollParent[ 0 ].scrollTop =
						scrolled = this.scrollParent[ 0 ].scrollTop + o.scrollSpeed;
				} else if ( event.pageY - this.overflowOffset.top < o.scrollSensitivity ) {
					this.scrollParent[ 0 ].scrollTop =
						scrolled = this.scrollParent[ 0 ].scrollTop - o.scrollSpeed;
				}

				if ( ( this.overflowOffset.left + this.scrollParent[ 0 ].offsetWidth ) -
						event.pageX < o.scrollSensitivity ) {
					this.scrollParent[ 0 ].scrollLeft = scrolled =
						this.scrollParent[ 0 ].scrollLeft + o.scrollSpeed;
				} else if ( event.pageX - this.overflowOffset.left < o.scrollSensitivity ) {
					this.scrollParent[ 0 ].scrollLeft = scrolled =
						this.scrollParent[ 0 ].scrollLeft - o.scrollSpeed;
				}

			} else {

				if ( event.pageY - this.document.scrollTop() < o.scrollSensitivity ) {
					scrolled = this.document.scrollTop( this.document.scrollTop() - o.scrollSpeed );
				} else if ( this.window.height() - ( event.pageY - this.document.scrollTop() ) <
						o.scrollSensitivity ) {
					scrolled = this.document.scrollTop( this.document.scrollTop() + o.scrollSpeed );
				}

				if ( event.pageX - this.document.scrollLeft() < o.scrollSensitivity ) {
					scrolled = this.document.scrollLeft(
						this.document.scrollLeft() - o.scrollSpeed
					);
				} else if ( this.window.width() - ( event.pageX - this.document.scrollLeft() ) <
						o.scrollSensitivity ) {
					scrolled = this.document.scrollLeft(
						this.document.scrollLeft() + o.scrollSpeed
					);
				}

			}

			if ( scrolled !== false && $.ui.ddmanager && !o.dropBehaviour ) {
				$.ui.ddmanager.prepareOffsets( this, event );
			}
		}

		//Regenerate the absolute position used for position checks
		this.positionAbs = this._convertPositionTo( "absolute" );

		//Set the helper position
		if ( !this.options.axis || this.options.axis !== "y" ) {
			this.helper[ 0 ].style.left = this.position.left + "px";
		}
		if ( !this.options.axis || this.options.axis !== "x" ) {
			this.helper[ 0 ].style.top = this.position.top + "px";
		}

		//Rearrange
		for ( i = this.items.length - 1; i >= 0; i-- ) {

			//Cache variables and intersection, continue if no intersection
			item = this.items[ i ];
			itemElement = item.item[ 0 ];
			intersection = this._intersectsWithPointer( item );
			if ( !intersection ) {
				continue;
			}

			// Only put the placeholder inside the current Container, skip all
			// items from other containers. This works because when moving
			// an item from one container to another the
			// currentContainer is switched before the placeholder is moved.
			//
			// Without this, moving items in "sub-sortables" can cause
			// the placeholder to jitter between the outer and inner container.
			if ( item.instance !== this.currentContainer ) {
				continue;
			}

			// Cannot intersect with itself
			// no useless actions that have been done before
			// no action if the item moved is the parent of the item checked
			if ( itemElement !== this.currentItem[ 0 ] &&
				this.placeholder[ intersection === 1 ? "next" : "prev" ]()[ 0 ] !== itemElement &&
				!$.contains( this.placeholder[ 0 ], itemElement ) &&
				( this.options.type === "semi-dynamic" ?
					!$.contains( this.element[ 0 ], itemElement ) :
					true
				)
			) {

				this.direction = intersection === 1 ? "down" : "up";

				if ( this.options.tolerance === "pointer" || this._intersectsWithSides( item ) ) {
					this._rearrange( event, item );
				} else {
					break;
				}

				this._trigger( "change", event, this._uiHash() );
				break;
			}
		}

		//Post events to containers
		this._contactContainers( event );

		//Interconnect with droppables
		if ( $.ui.ddmanager ) {
			$.ui.ddmanager.drag( this, event );
		}

		//Call callbacks
		this._trigger( "sort", event, this._uiHash() );

		this.lastPositionAbs = this.positionAbs;
		return false;

	},

	_mouseStop: function( event, noPropagation ) {

		if ( !event ) {
			return;
		}

		//If we are using droppables, inform the manager about the drop
		if ( $.ui.ddmanager && !this.options.dropBehaviour ) {
			$.ui.ddmanager.drop( this, event );
		}

		if ( this.options.revert ) {
			var that = this,
				cur = this.placeholder.offset(),
				axis = this.options.axis,
				animation = {};

			if ( !axis || axis === "x" ) {
				animation.left = cur.left - this.offset.parent.left - this.margins.left +
					( this.offsetParent[ 0 ] === this.document[ 0 ].body ?
						0 :
						this.offsetParent[ 0 ].scrollLeft
					);
			}
			if ( !axis || axis === "y" ) {
				animation.top = cur.top - this.offset.parent.top - this.margins.top +
					( this.offsetParent[ 0 ] === this.document[ 0 ].body ?
						0 :
						this.offsetParent[ 0 ].scrollTop
					);
			}
			this.reverting = true;
			$( this.helper ).animate(
				animation,
				parseInt( this.options.revert, 10 ) || 500,
				function() {
					that._clear( event );
				}
			);
		} else {
			this._clear( event, noPropagation );
		}

		return false;

	},

	cancel: function() {

		if ( this.dragging ) {

			this._mouseUp( new $.Event( "mouseup", { target: null } ) );

			if ( this.options.helper === "original" ) {
				this.currentItem.css( this._storedCSS );
				this._removeClass( this.currentItem, "ui-sortable-helper" );
			} else {
				this.currentItem.show();
			}

			//Post deactivating events to containers
			for ( var i = this.containers.length - 1; i >= 0; i-- ) {
				this.containers[ i ]._trigger( "deactivate", null, this._uiHash( this ) );
				if ( this.containers[ i ].containerCache.over ) {
					this.containers[ i ]._trigger( "out", null, this._uiHash( this ) );
					this.containers[ i ].containerCache.over = 0;
				}
			}

		}

		if ( this.placeholder ) {

			//$(this.placeholder[0]).remove(); would have been the jQuery way - unfortunately,
			// it unbinds ALL events from the original node!
			if ( this.placeholder[ 0 ].parentNode ) {
				this.placeholder[ 0 ].parentNode.removeChild( this.placeholder[ 0 ] );
			}
			if ( this.options.helper !== "original" && this.helper &&
					this.helper[ 0 ].parentNode ) {
				this.helper.remove();
			}

			$.extend( this, {
				helper: null,
				dragging: false,
				reverting: false,
				_noFinalSort: null
			} );

			if ( this.domPosition.prev ) {
				$( this.domPosition.prev ).after( this.currentItem );
			} else {
				$( this.domPosition.parent ).prepend( this.currentItem );
			}
		}

		return this;

	},

	serialize: function( o ) {

		var items = this._getItemsAsjQuery( o && o.connected ),
			str = [];
		o = o || {};

		$( items ).each( function() {
			var res = ( $( o.item || this ).attr( o.attribute || "id" ) || "" )
				.match( o.expression || ( /(.+)[\-=_](.+)/ ) );
			if ( res ) {
				str.push(
					( o.key || res[ 1 ] + "[]" ) +
					"=" + ( o.key && o.expression ? res[ 1 ] : res[ 2 ] ) );
			}
		} );

		if ( !str.length && o.key ) {
			str.push( o.key + "=" );
		}

		return str.join( "&" );

	},

	toArray: function( o ) {

		var items = this._getItemsAsjQuery( o && o.connected ),
			ret = [];

		o = o || {};

		items.each( function() {
			ret.push( $( o.item || this ).attr( o.attribute || "id" ) || "" );
		} );
		return ret;

	},

	/* Be careful with the following core functions */
	_intersectsWith: function( item ) {

		var x1 = this.positionAbs.left,
			x2 = x1 + this.helperProportions.width,
			y1 = this.positionAbs.top,
			y2 = y1 + this.helperProportions.height,
			l = item.left,
			r = l + item.width,
			t = item.top,
			b = t + item.height,
			dyClick = this.offset.click.top,
			dxClick = this.offset.click.left,
			isOverElementHeight = ( this.options.axis === "x" ) || ( ( y1 + dyClick ) > t &&
				( y1 + dyClick ) < b ),
			isOverElementWidth = ( this.options.axis === "y" ) || ( ( x1 + dxClick ) > l &&
				( x1 + dxClick ) < r ),
			isOverElement = isOverElementHeight && isOverElementWidth;

		if ( this.options.tolerance === "pointer" ||
			this.options.forcePointerForContainers ||
			( this.options.tolerance !== "pointer" &&
				this.helperProportions[ this.floating ? "width" : "height" ] >
				item[ this.floating ? "width" : "height" ] )
		) {
			return isOverElement;
		} else {

			return ( l < x1 + ( this.helperProportions.width / 2 ) && // Right Half
				x2 - ( this.helperProportions.width / 2 ) < r && // Left Half
				t < y1 + ( this.helperProportions.height / 2 ) && // Bottom Half
				y2 - ( this.helperProportions.height / 2 ) < b ); // Top Half

		}
	},

	_intersectsWithPointer: function( item ) {
		var verticalDirection, horizontalDirection,
			isOverElementHeight = ( this.options.axis === "x" ) ||
				this._isOverAxis(
					this.positionAbs.top + this.offset.click.top, item.top, item.height ),
			isOverElementWidth = ( this.options.axis === "y" ) ||
				this._isOverAxis(
					this.positionAbs.left + this.offset.click.left, item.left, item.width ),
			isOverElement = isOverElementHeight && isOverElementWidth;

		if ( !isOverElement ) {
			return false;
		}

		verticalDirection = this._getDragVerticalDirection();
		horizontalDirection = this._getDragHorizontalDirection();

		return this.floating ?
			( ( horizontalDirection === "right" || verticalDirection === "down" ) ? 2 : 1 )
			: ( verticalDirection && ( verticalDirection === "down" ? 2 : 1 ) );

	},

	_intersectsWithSides: function( item ) {

		var isOverBottomHalf = this._isOverAxis( this.positionAbs.top +
				this.offset.click.top, item.top + ( item.height / 2 ), item.height ),
			isOverRightHalf = this._isOverAxis( this.positionAbs.left +
				this.offset.click.left, item.left + ( item.width / 2 ), item.width ),
			verticalDirection = this._getDragVerticalDirection(),
			horizontalDirection = this._getDragHorizontalDirection();

		if ( this.floating && horizontalDirection ) {
			return ( ( horizontalDirection === "right" && isOverRightHalf ) ||
				( horizontalDirection === "left" && !isOverRightHalf ) );
		} else {
			return verticalDirection && ( ( verticalDirection === "down" && isOverBottomHalf ) ||
				( verticalDirection === "up" && !isOverBottomHalf ) );
		}

	},

	_getDragVerticalDirection: function() {
		var delta = this.positionAbs.top - this.lastPositionAbs.top;
		return delta !== 0 && ( delta > 0 ? "down" : "up" );
	},

	_getDragHorizontalDirection: function() {
		var delta = this.positionAbs.left - this.lastPositionAbs.left;
		return delta !== 0 && ( delta > 0 ? "right" : "left" );
	},

	refresh: function( event ) {
		this._refreshItems( event );
		this._setHandleClassName();
		this.refreshPositions();
		return this;
	},

	_connectWith: function() {
		var options = this.options;
		return options.connectWith.constructor === String ?
			[ options.connectWith ] :
			options.connectWith;
	},

	_getItemsAsjQuery: function( connected ) {

		var i, j, cur, inst,
			items = [],
			queries = [],
			connectWith = this._connectWith();

		if ( connectWith && connected ) {
			for ( i = connectWith.length - 1; i >= 0; i-- ) {
				cur = $( connectWith[ i ], this.document[ 0 ] );
				for ( j = cur.length - 1; j >= 0; j-- ) {
					inst = $.data( cur[ j ], this.widgetFullName );
					if ( inst && inst !== this && !inst.options.disabled ) {
						queries.push( [ $.isFunction( inst.options.items ) ?
							inst.options.items.call( inst.element ) :
							$( inst.options.items, inst.element )
								.not( ".ui-sortable-helper" )
								.not( ".ui-sortable-placeholder" ), inst ] );
					}
				}
			}
		}

		queries.push( [ $.isFunction( this.options.items ) ?
			this.options.items
				.call( this.element, null, { options: this.options, item: this.currentItem } ) :
			$( this.options.items, this.element )
				.not( ".ui-sortable-helper" )
				.not( ".ui-sortable-placeholder" ), this ] );

		function addItems() {
			items.push( this );
		}
		for ( i = queries.length - 1; i >= 0; i-- ) {
			queries[ i ][ 0 ].each( addItems );
		}

		return $( items );

	},

	_removeCurrentsFromItems: function() {

		var list = this.currentItem.find( ":data(" + this.widgetName + "-item)" );

		this.items = $.grep( this.items, function( item ) {
			for ( var j = 0; j < list.length; j++ ) {
				if ( list[ j ] === item.item[ 0 ] ) {
					return false;
				}
			}
			return true;
		} );

	},

	_refreshItems: function( event ) {

		this.items = [];
		this.containers = [ this ];

		var i, j, cur, inst, targetData, _queries, item, queriesLength,
			items = this.items,
			queries = [ [ $.isFunction( this.options.items ) ?
				this.options.items.call( this.element[ 0 ], event, { item: this.currentItem } ) :
				$( this.options.items, this.element ), this ] ],
			connectWith = this._connectWith();

		//Shouldn't be run the first time through due to massive slow-down
		if ( connectWith && this.ready ) {
			for ( i = connectWith.length - 1; i >= 0; i-- ) {
				cur = $( connectWith[ i ], this.document[ 0 ] );
				for ( j = cur.length - 1; j >= 0; j-- ) {
					inst = $.data( cur[ j ], this.widgetFullName );
					if ( inst && inst !== this && !inst.options.disabled ) {
						queries.push( [ $.isFunction( inst.options.items ) ?
							inst.options.items
								.call( inst.element[ 0 ], event, { item: this.currentItem } ) :
							$( inst.options.items, inst.element ), inst ] );
						this.containers.push( inst );
					}
				}
			}
		}

		for ( i = queries.length - 1; i >= 0; i-- ) {
			targetData = queries[ i ][ 1 ];
			_queries = queries[ i ][ 0 ];

			for ( j = 0, queriesLength = _queries.length; j < queriesLength; j++ ) {
				item = $( _queries[ j ] );

				// Data for target checking (mouse manager)
				item.data( this.widgetName + "-item", targetData );

				items.push( {
					item: item,
					instance: targetData,
					width: 0, height: 0,
					left: 0, top: 0
				} );
			}
		}

	},

	refreshPositions: function( fast ) {

		// Determine whether items are being displayed horizontally
		this.floating = this.items.length ?
			this.options.axis === "x" || this._isFloating( this.items[ 0 ].item ) :
			false;

		//This has to be redone because due to the item being moved out/into the offsetParent,
		// the offsetParent's position will change
		if ( this.offsetParent && this.helper ) {
			this.offset.parent = this._getParentOffset();
		}

		var i, item, t, p;

		for ( i = this.items.length - 1; i >= 0; i-- ) {
			item = this.items[ i ];

			//We ignore calculating positions of all connected containers when we're not over them
			if ( item.instance !== this.currentContainer && this.currentContainer &&
					item.item[ 0 ] !== this.currentItem[ 0 ] ) {
				continue;
			}

			t = this.options.toleranceElement ?
				$( this.options.toleranceElement, item.item ) :
				item.item;

			if ( !fast ) {
				item.width = t.outerWidth();
				item.height = t.outerHeight();
			}

			p = t.offset();
			item.left = p.left;
			item.top = p.top;
		}

		if ( this.options.custom && this.options.custom.refreshContainers ) {
			this.options.custom.refreshContainers.call( this );
		} else {
			for ( i = this.containers.length - 1; i >= 0; i-- ) {
				p = this.containers[ i ].element.offset();
				this.containers[ i ].containerCache.left = p.left;
				this.containers[ i ].containerCache.top = p.top;
				this.containers[ i ].containerCache.width =
					this.containers[ i ].element.outerWidth();
				this.containers[ i ].containerCache.height =
					this.containers[ i ].element.outerHeight();
			}
		}

		return this;
	},

	_createPlaceholder: function( that ) {
		that = that || this;
		var className,
			o = that.options;

		if ( !o.placeholder || o.placeholder.constructor === String ) {
			className = o.placeholder;
			o.placeholder = {
				element: function() {

					var nodeName = that.currentItem[ 0 ].nodeName.toLowerCase(),
						element = $( "<" + nodeName + ">", that.document[ 0 ] );

						that._addClass( element, "ui-sortable-placeholder",
								className || that.currentItem[ 0 ].className )
							._removeClass( element, "ui-sortable-helper" );

					if ( nodeName === "tbody" ) {
						that._createTrPlaceholder(
							that.currentItem.find( "tr" ).eq( 0 ),
							$( "<tr>", that.document[ 0 ] ).appendTo( element )
						);
					} else if ( nodeName === "tr" ) {
						that._createTrPlaceholder( that.currentItem, element );
					} else if ( nodeName === "img" ) {
						element.attr( "src", that.currentItem.attr( "src" ) );
					}

					if ( !className ) {
						element.css( "visibility", "hidden" );
					}

					return element;
				},
				update: function( container, p ) {

					// 1. If a className is set as 'placeholder option, we don't force sizes -
					// the class is responsible for that
					// 2. The option 'forcePlaceholderSize can be enabled to force it even if a
					// class name is specified
					if ( className && !o.forcePlaceholderSize ) {
						return;
					}

					//If the element doesn't have a actual height by itself (without styles coming
					// from a stylesheet), it receives the inline height from the dragged item
					if ( !p.height() ) {
						p.height(
							that.currentItem.innerHeight() -
							parseInt( that.currentItem.css( "paddingTop" ) || 0, 10 ) -
							parseInt( that.currentItem.css( "paddingBottom" ) || 0, 10 ) );
					}
					if ( !p.width() ) {
						p.width(
							that.currentItem.innerWidth() -
							parseInt( that.currentItem.css( "paddingLeft" ) || 0, 10 ) -
							parseInt( that.currentItem.css( "paddingRight" ) || 0, 10 ) );
					}
				}
			};
		}

		//Create the placeholder
		that.placeholder = $( o.placeholder.element.call( that.element, that.currentItem ) );

		//Append it after the actual current item
		that.currentItem.after( that.placeholder );

		//Update the size of the placeholder (TODO: Logic to fuzzy, see line 316/317)
		o.placeholder.update( that, that.placeholder );

	},

	_createTrPlaceholder: function( sourceTr, targetTr ) {
		var that = this;

		sourceTr.children().each( function() {
			$( "<td>&#160;</td>", that.document[ 0 ] )
				.attr( "colspan", $( this ).attr( "colspan" ) || 1 )
				.appendTo( targetTr );
		} );
	},

	_contactContainers: function( event ) {
		var i, j, dist, itemWithLeastDistance, posProperty, sizeProperty, cur, nearBottom,
			floating, axis,
			innermostContainer = null,
			innermostIndex = null;

		// Get innermost container that intersects with item
		for ( i = this.containers.length - 1; i >= 0; i-- ) {

			// Never consider a container that's located within the item itself
			if ( $.contains( this.currentItem[ 0 ], this.containers[ i ].element[ 0 ] ) ) {
				continue;
			}

			if ( this._intersectsWith( this.containers[ i ].containerCache ) ) {

				// If we've already found a container and it's more "inner" than this, then continue
				if ( innermostContainer &&
						$.contains(
							this.containers[ i ].element[ 0 ],
							innermostContainer.element[ 0 ] ) ) {
					continue;
				}

				innermostContainer = this.containers[ i ];
				innermostIndex = i;

			} else {

				// container doesn't intersect. trigger "out" event if necessary
				if ( this.containers[ i ].containerCache.over ) {
					this.containers[ i ]._trigger( "out", event, this._uiHash( this ) );
					this.containers[ i ].containerCache.over = 0;
				}
			}

		}

		// If no intersecting containers found, return
		if ( !innermostContainer ) {
			return;
		}

		// Move the item into the container if it's not there already
		if ( this.containers.length === 1 ) {
			if ( !this.containers[ innermostIndex ].containerCache.over ) {
				this.containers[ innermostIndex ]._trigger( "over", event, this._uiHash( this ) );
				this.containers[ innermostIndex ].containerCache.over = 1;
			}
		} else {

			// When entering a new container, we will find the item with the least distance and
			// append our item near it
			dist = 10000;
			itemWithLeastDistance = null;
			floating = innermostContainer.floating || this._isFloating( this.currentItem );
			posProperty = floating ? "left" : "top";
			sizeProperty = floating ? "width" : "height";
			axis = floating ? "pageX" : "pageY";

			for ( j = this.items.length - 1; j >= 0; j-- ) {
				if ( !$.contains(
						this.containers[ innermostIndex ].element[ 0 ], this.items[ j ].item[ 0 ] )
				) {
					continue;
				}
				if ( this.items[ j ].item[ 0 ] === this.currentItem[ 0 ] ) {
					continue;
				}

				cur = this.items[ j ].item.offset()[ posProperty ];
				nearBottom = false;
				if ( event[ axis ] - cur > this.items[ j ][ sizeProperty ] / 2 ) {
					nearBottom = true;
				}

				if ( Math.abs( event[ axis ] - cur ) < dist ) {
					dist = Math.abs( event[ axis ] - cur );
					itemWithLeastDistance = this.items[ j ];
					this.direction = nearBottom ? "up" : "down";
				}
			}

			//Check if dropOnEmpty is enabled
			if ( !itemWithLeastDistance && !this.options.dropOnEmpty ) {
				return;
			}

			if ( this.currentContainer === this.containers[ innermostIndex ] ) {
				if ( !this.currentContainer.containerCache.over ) {
					this.containers[ innermostIndex ]._trigger( "over", event, this._uiHash() );
					this.currentContainer.containerCache.over = 1;
				}
				return;
			}

			itemWithLeastDistance ?
				this._rearrange( event, itemWithLeastDistance, null, true ) :
				this._rearrange( event, null, this.containers[ innermostIndex ].element, true );
			this._trigger( "change", event, this._uiHash() );
			this.containers[ innermostIndex ]._trigger( "change", event, this._uiHash( this ) );
			this.currentContainer = this.containers[ innermostIndex ];

			//Update the placeholder
			this.options.placeholder.update( this.currentContainer, this.placeholder );

			this.containers[ innermostIndex ]._trigger( "over", event, this._uiHash( this ) );
			this.containers[ innermostIndex ].containerCache.over = 1;
		}

	},

	_createHelper: function( event ) {

		var o = this.options,
			helper = $.isFunction( o.helper ) ?
				$( o.helper.apply( this.element[ 0 ], [ event, this.currentItem ] ) ) :
				( o.helper === "clone" ? this.currentItem.clone() : this.currentItem );

		//Add the helper to the DOM if that didn't happen already
		if ( !helper.parents( "body" ).length ) {
			$( o.appendTo !== "parent" ?
				o.appendTo :
				this.currentItem[ 0 ].parentNode )[ 0 ].appendChild( helper[ 0 ] );
		}

		if ( helper[ 0 ] === this.currentItem[ 0 ] ) {
			this._storedCSS = {
				width: this.currentItem[ 0 ].style.width,
				height: this.currentItem[ 0 ].style.height,
				position: this.currentItem.css( "position" ),
				top: this.currentItem.css( "top" ),
				left: this.currentItem.css( "left" )
			};
		}

		if ( !helper[ 0 ].style.width || o.forceHelperSize ) {
			helper.width( this.currentItem.width() );
		}
		if ( !helper[ 0 ].style.height || o.forceHelperSize ) {
			helper.height( this.currentItem.height() );
		}

		return helper;

	},

	_adjustOffsetFromHelper: function( obj ) {
		if ( typeof obj === "string" ) {
			obj = obj.split( " " );
		}
		if ( $.isArray( obj ) ) {
			obj = { left: +obj[ 0 ], top: +obj[ 1 ] || 0 };
		}
		if ( "left" in obj ) {
			this.offset.click.left = obj.left + this.margins.left;
		}
		if ( "right" in obj ) {
			this.offset.click.left = this.helperProportions.width - obj.right + this.margins.left;
		}
		if ( "top" in obj ) {
			this.offset.click.top = obj.top + this.margins.top;
		}
		if ( "bottom" in obj ) {
			this.offset.click.top = this.helperProportions.height - obj.bottom + this.margins.top;
		}
	},

	_getParentOffset: function() {

		//Get the offsetParent and cache its position
		this.offsetParent = this.helper.offsetParent();
		var po = this.offsetParent.offset();

		// This is a special case where we need to modify a offset calculated on start, since the
		// following happened:
		// 1. The position of the helper is absolute, so it's position is calculated based on the
		// next positioned parent
		// 2. The actual offset parent is a child of the scroll parent, and the scroll parent isn't
		// the document, which means that the scroll is included in the initial calculation of the
		// offset of the parent, and never recalculated upon drag
		if ( this.cssPosition === "absolute" && this.scrollParent[ 0 ] !== this.document[ 0 ] &&
				$.contains( this.scrollParent[ 0 ], this.offsetParent[ 0 ] ) ) {
			po.left += this.scrollParent.scrollLeft();
			po.top += this.scrollParent.scrollTop();
		}

		// This needs to be actually done for all browsers, since pageX/pageY includes this
		// information with an ugly IE fix
		if ( this.offsetParent[ 0 ] === this.document[ 0 ].body ||
				( this.offsetParent[ 0 ].tagName &&
				this.offsetParent[ 0 ].tagName.toLowerCase() === "html" && $.ui.ie ) ) {
			po = { top: 0, left: 0 };
		}

		return {
			top: po.top + ( parseInt( this.offsetParent.css( "borderTopWidth" ), 10 ) || 0 ),
			left: po.left + ( parseInt( this.offsetParent.css( "borderLeftWidth" ), 10 ) || 0 )
		};

	},

	_getRelativeOffset: function() {

		if ( this.cssPosition === "relative" ) {
			var p = this.currentItem.position();
			return {
				top: p.top - ( parseInt( this.helper.css( "top" ), 10 ) || 0 ) +
					this.scrollParent.scrollTop(),
				left: p.left - ( parseInt( this.helper.css( "left" ), 10 ) || 0 ) +
					this.scrollParent.scrollLeft()
			};
		} else {
			return { top: 0, left: 0 };
		}

	},

	_cacheMargins: function() {
		this.margins = {
			left: ( parseInt( this.currentItem.css( "marginLeft" ), 10 ) || 0 ),
			top: ( parseInt( this.currentItem.css( "marginTop" ), 10 ) || 0 )
		};
	},

	_cacheHelperProportions: function() {
		this.helperProportions = {
			width: this.helper.outerWidth(),
			height: this.helper.outerHeight()
		};
	},

	_setContainment: function() {

		var ce, co, over,
			o = this.options;
		if ( o.containment === "parent" ) {
			o.containment = this.helper[ 0 ].parentNode;
		}
		if ( o.containment === "document" || o.containment === "window" ) {
			this.containment = [
				0 - this.offset.relative.left - this.offset.parent.left,
				0 - this.offset.relative.top - this.offset.parent.top,
				o.containment === "document" ?
					this.document.width() :
					this.window.width() - this.helperProportions.width - this.margins.left,
				( o.containment === "document" ?
					( this.document.height() || document.body.parentNode.scrollHeight ) :
					this.window.height() || this.document[ 0 ].body.parentNode.scrollHeight
				) - this.helperProportions.height - this.margins.top
			];
		}

		if ( !( /^(document|window|parent)$/ ).test( o.containment ) ) {
			ce = $( o.containment )[ 0 ];
			co = $( o.containment ).offset();
			over = ( $( ce ).css( "overflow" ) !== "hidden" );

			this.containment = [
				co.left + ( parseInt( $( ce ).css( "borderLeftWidth" ), 10 ) || 0 ) +
					( parseInt( $( ce ).css( "paddingLeft" ), 10 ) || 0 ) - this.margins.left,
				co.top + ( parseInt( $( ce ).css( "borderTopWidth" ), 10 ) || 0 ) +
					( parseInt( $( ce ).css( "paddingTop" ), 10 ) || 0 ) - this.margins.top,
				co.left + ( over ? Math.max( ce.scrollWidth, ce.offsetWidth ) : ce.offsetWidth ) -
					( parseInt( $( ce ).css( "borderLeftWidth" ), 10 ) || 0 ) -
					( parseInt( $( ce ).css( "paddingRight" ), 10 ) || 0 ) -
					this.helperProportions.width - this.margins.left,
				co.top + ( over ? Math.max( ce.scrollHeight, ce.offsetHeight ) : ce.offsetHeight ) -
					( parseInt( $( ce ).css( "borderTopWidth" ), 10 ) || 0 ) -
					( parseInt( $( ce ).css( "paddingBottom" ), 10 ) || 0 ) -
					this.helperProportions.height - this.margins.top
			];
		}

	},

	_convertPositionTo: function( d, pos ) {

		if ( !pos ) {
			pos = this.position;
		}
		var mod = d === "absolute" ? 1 : -1,
			scroll = this.cssPosition === "absolute" &&
				!( this.scrollParent[ 0 ] !== this.document[ 0 ] &&
				$.contains( this.scrollParent[ 0 ], this.offsetParent[ 0 ] ) ) ?
					this.offsetParent :
					this.scrollParent,
			scrollIsRootNode = ( /(html|body)/i ).test( scroll[ 0 ].tagName );

		return {
			top: (

				// The absolute mouse position
				pos.top	+

				// Only for relative positioned nodes: Relative offset from element to offset parent
				this.offset.relative.top * mod +

				// The offsetParent's offset without borders (offset + border)
				this.offset.parent.top * mod -
				( ( this.cssPosition === "fixed" ?
					-this.scrollParent.scrollTop() :
					( scrollIsRootNode ? 0 : scroll.scrollTop() ) ) * mod )
			),
			left: (

				// The absolute mouse position
				pos.left +

				// Only for relative positioned nodes: Relative offset from element to offset parent
				this.offset.relative.left * mod +

				// The offsetParent's offset without borders (offset + border)
				this.offset.parent.left * mod	-
				( ( this.cssPosition === "fixed" ?
					-this.scrollParent.scrollLeft() : scrollIsRootNode ? 0 :
					scroll.scrollLeft() ) * mod )
			)
		};

	},

	_generatePosition: function( event ) {

		var top, left,
			o = this.options,
			pageX = event.pageX,
			pageY = event.pageY,
			scroll = this.cssPosition === "absolute" &&
				!( this.scrollParent[ 0 ] !== this.document[ 0 ] &&
				$.contains( this.scrollParent[ 0 ], this.offsetParent[ 0 ] ) ) ?
					this.offsetParent :
					this.scrollParent,
				scrollIsRootNode = ( /(html|body)/i ).test( scroll[ 0 ].tagName );

		// This is another very weird special case that only happens for relative elements:
		// 1. If the css position is relative
		// 2. and the scroll parent is the document or similar to the offset parent
		// we have to refresh the relative offset during the scroll so there are no jumps
		if ( this.cssPosition === "relative" && !( this.scrollParent[ 0 ] !== this.document[ 0 ] &&
				this.scrollParent[ 0 ] !== this.offsetParent[ 0 ] ) ) {
			this.offset.relative = this._getRelativeOffset();
		}

		/*
		 * - Position constraining -
		 * Constrain the position to a mix of grid, containment.
		 */

		if ( this.originalPosition ) { //If we are not dragging yet, we won't check for options

			if ( this.containment ) {
				if ( event.pageX - this.offset.click.left < this.containment[ 0 ] ) {
					pageX = this.containment[ 0 ] + this.offset.click.left;
				}
				if ( event.pageY - this.offset.click.top < this.containment[ 1 ] ) {
					pageY = this.containment[ 1 ] + this.offset.click.top;
				}
				if ( event.pageX - this.offset.click.left > this.containment[ 2 ] ) {
					pageX = this.containment[ 2 ] + this.offset.click.left;
				}
				if ( event.pageY - this.offset.click.top > this.containment[ 3 ] ) {
					pageY = this.containment[ 3 ] + this.offset.click.top;
				}
			}

			if ( o.grid ) {
				top = this.originalPageY + Math.round( ( pageY - this.originalPageY ) /
					o.grid[ 1 ] ) * o.grid[ 1 ];
				pageY = this.containment ?
					( ( top - this.offset.click.top >= this.containment[ 1 ] &&
						top - this.offset.click.top <= this.containment[ 3 ] ) ?
							top :
							( ( top - this.offset.click.top >= this.containment[ 1 ] ) ?
								top - o.grid[ 1 ] : top + o.grid[ 1 ] ) ) :
								top;

				left = this.originalPageX + Math.round( ( pageX - this.originalPageX ) /
					o.grid[ 0 ] ) * o.grid[ 0 ];
				pageX = this.containment ?
					( ( left - this.offset.click.left >= this.containment[ 0 ] &&
						left - this.offset.click.left <= this.containment[ 2 ] ) ?
							left :
							( ( left - this.offset.click.left >= this.containment[ 0 ] ) ?
								left - o.grid[ 0 ] : left + o.grid[ 0 ] ) ) :
								left;
			}

		}

		return {
			top: (

				// The absolute mouse position
				pageY -

				// Click offset (relative to the element)
				this.offset.click.top -

				// Only for relative positioned nodes: Relative offset from element to offset parent
				this.offset.relative.top -

				// The offsetParent's offset without borders (offset + border)
				this.offset.parent.top +
				( ( this.cssPosition === "fixed" ?
					-this.scrollParent.scrollTop() :
					( scrollIsRootNode ? 0 : scroll.scrollTop() ) ) )
			),
			left: (

				// The absolute mouse position
				pageX -

				// Click offset (relative to the element)
				this.offset.click.left -

				// Only for relative positioned nodes: Relative offset from element to offset parent
				this.offset.relative.left -

				// The offsetParent's offset without borders (offset + border)
				this.offset.parent.left +
				( ( this.cssPosition === "fixed" ?
					-this.scrollParent.scrollLeft() :
					scrollIsRootNode ? 0 : scroll.scrollLeft() ) )
			)
		};

	},

	_rearrange: function( event, i, a, hardRefresh ) {

		a ? a[ 0 ].appendChild( this.placeholder[ 0 ] ) :
			i.item[ 0 ].parentNode.insertBefore( this.placeholder[ 0 ],
				( this.direction === "down" ? i.item[ 0 ] : i.item[ 0 ].nextSibling ) );

		//Various things done here to improve the performance:
		// 1. we create a setTimeout, that calls refreshPositions
		// 2. on the instance, we have a counter variable, that get's higher after every append
		// 3. on the local scope, we copy the counter variable, and check in the timeout,
		// if it's still the same
		// 4. this lets only the last addition to the timeout stack through
		this.counter = this.counter ? ++this.counter : 1;
		var counter = this.counter;

		this._delay( function() {
			if ( counter === this.counter ) {

				//Precompute after each DOM insertion, NOT on mousemove
				this.refreshPositions( !hardRefresh );
			}
		} );

	},

	_clear: function( event, noPropagation ) {

		this.reverting = false;

		// We delay all events that have to be triggered to after the point where the placeholder
		// has been removed and everything else normalized again
		var i,
			delayedTriggers = [];

		// We first have to update the dom position of the actual currentItem
		// Note: don't do it if the current item is already removed (by a user), or it gets
		// reappended (see #4088)
		if ( !this._noFinalSort && this.currentItem.parent().length ) {
			this.placeholder.before( this.currentItem );
		}
		this._noFinalSort = null;

		if ( this.helper[ 0 ] === this.currentItem[ 0 ] ) {
			for ( i in this._storedCSS ) {
				if ( this._storedCSS[ i ] === "auto" || this._storedCSS[ i ] === "static" ) {
					this._storedCSS[ i ] = "";
				}
			}
			this.currentItem.css( this._storedCSS );
			this._removeClass( this.currentItem, "ui-sortable-helper" );
		} else {
			this.currentItem.show();
		}

		if ( this.fromOutside && !noPropagation ) {
			delayedTriggers.push( function( event ) {
				this._trigger( "receive", event, this._uiHash( this.fromOutside ) );
			} );
		}
		if ( ( this.fromOutside ||
				this.domPosition.prev !==
				this.currentItem.prev().not( ".ui-sortable-helper" )[ 0 ] ||
				this.domPosition.parent !== this.currentItem.parent()[ 0 ] ) && !noPropagation ) {

			// Trigger update callback if the DOM position has changed
			delayedTriggers.push( function( event ) {
				this._trigger( "update", event, this._uiHash() );
			} );
		}

		// Check if the items Container has Changed and trigger appropriate
		// events.
		if ( this !== this.currentContainer ) {
			if ( !noPropagation ) {
				delayedTriggers.push( function( event ) {
					this._trigger( "remove", event, this._uiHash() );
				} );
				delayedTriggers.push( ( function( c ) {
					return function( event ) {
						c._trigger( "receive", event, this._uiHash( this ) );
					};
				} ).call( this, this.currentContainer ) );
				delayedTriggers.push( ( function( c ) {
					return function( event ) {
						c._trigger( "update", event, this._uiHash( this ) );
					};
				} ).call( this, this.currentContainer ) );
			}
		}

		//Post events to containers
		function delayEvent( type, instance, container ) {
			return function( event ) {
				container._trigger( type, event, instance._uiHash( instance ) );
			};
		}
		for ( i = this.containers.length - 1; i >= 0; i-- ) {
			if ( !noPropagation ) {
				delayedTriggers.push( delayEvent( "deactivate", this, this.containers[ i ] ) );
			}
			if ( this.containers[ i ].containerCache.over ) {
				delayedTriggers.push( delayEvent( "out", this, this.containers[ i ] ) );
				this.containers[ i ].containerCache.over = 0;
			}
		}

		//Do what was originally in plugins
		if ( this.storedCursor ) {
			this.document.find( "body" ).css( "cursor", this.storedCursor );
			this.storedStylesheet.remove();
		}
		if ( this._storedOpacity ) {
			this.helper.css( "opacity", this._storedOpacity );
		}
		if ( this._storedZIndex ) {
			this.helper.css( "zIndex", this._storedZIndex === "auto" ? "" : this._storedZIndex );
		}

		this.dragging = false;

		if ( !noPropagation ) {
			this._trigger( "beforeStop", event, this._uiHash() );
		}

		//$(this.placeholder[0]).remove(); would have been the jQuery way - unfortunately,
		// it unbinds ALL events from the original node!
		this.placeholder[ 0 ].parentNode.removeChild( this.placeholder[ 0 ] );

		if ( !this.cancelHelperRemoval ) {
			if ( this.helper[ 0 ] !== this.currentItem[ 0 ] ) {
				this.helper.remove();
			}
			this.helper = null;
		}

		if ( !noPropagation ) {
			for ( i = 0; i < delayedTriggers.length; i++ ) {

				// Trigger all delayed events
				delayedTriggers[ i ].call( this, event );
			}
			this._trigger( "stop", event, this._uiHash() );
		}

		this.fromOutside = false;
		return !this.cancelHelperRemoval;

	},

	_trigger: function() {
		if ( $.Widget.prototype._trigger.apply( this, arguments ) === false ) {
			this.cancel();
		}
	},

	_uiHash: function( _inst ) {
		var inst = _inst || this;
		return {
			helper: inst.helper,
			placeholder: inst.placeholder || $( [] ),
			position: inst.position,
			originalPosition: inst.originalPosition,
			offset: inst.positionAbs,
			item: inst.currentItem,
			sender: _inst ? _inst.element : null
		};
	}

} );


/*!
 * jQuery UI Spinner 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Spinner
//>>group: Widgets
//>>description: Displays buttons to easily input numbers via the keyboard or mouse.
//>>docs: http://api.jqueryui.com/spinner/
//>>demos: http://jqueryui.com/spinner/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/spinner.css
//>>css.theme: ../../themes/base/theme.css



function spinnerModifer( fn ) {
	return function() {
		var previous = this.element.val();
		fn.apply( this, arguments );
		this._refresh();
		if ( previous !== this.element.val() ) {
			this._trigger( "change" );
		}
	};
}

$.widget( "ui.spinner", {
	version: "1.12.1",
	defaultElement: "<input>",
	widgetEventPrefix: "spin",
	options: {
		classes: {
			"ui-spinner": "ui-corner-all",
			"ui-spinner-down": "ui-corner-br",
			"ui-spinner-up": "ui-corner-tr"
		},
		culture: null,
		icons: {
			down: "ui-icon-triangle-1-s",
			up: "ui-icon-triangle-1-n"
		},
		incremental: true,
		max: null,
		min: null,
		numberFormat: null,
		page: 10,
		step: 1,

		change: null,
		spin: null,
		start: null,
		stop: null
	},

	_create: function() {

		// handle string values that need to be parsed
		this._setOption( "max", this.options.max );
		this._setOption( "min", this.options.min );
		this._setOption( "step", this.options.step );

		// Only format if there is a value, prevents the field from being marked
		// as invalid in Firefox, see #9573.
		if ( this.value() !== "" ) {

			// Format the value, but don't constrain.
			this._value( this.element.val(), true );
		}

		this._draw();
		this._on( this._events );
		this._refresh();

		// Turning off autocomplete prevents the browser from remembering the
		// value when navigating through history, so we re-enable autocomplete
		// if the page is unloaded before the widget is destroyed. #7790
		this._on( this.window, {
			beforeunload: function() {
				this.element.removeAttr( "autocomplete" );
			}
		} );
	},

	_getCreateOptions: function() {
		var options = this._super();
		var element = this.element;

		$.each( [ "min", "max", "step" ], function( i, option ) {
			var value = element.attr( option );
			if ( value != null && value.length ) {
				options[ option ] = value;
			}
		} );

		return options;
	},

	_events: {
		keydown: function( event ) {
			if ( this._start( event ) && this._keydown( event ) ) {
				event.preventDefault();
			}
		},
		keyup: "_stop",
		focus: function() {
			this.previous = this.element.val();
		},
		blur: function( event ) {
			if ( this.cancelBlur ) {
				delete this.cancelBlur;
				return;
			}

			this._stop();
			this._refresh();
			if ( this.previous !== this.element.val() ) {
				this._trigger( "change", event );
			}
		},
		mousewheel: function( event, delta ) {
			if ( !delta ) {
				return;
			}
			if ( !this.spinning && !this._start( event ) ) {
				return false;
			}

			this._spin( ( delta > 0 ? 1 : -1 ) * this.options.step, event );
			clearTimeout( this.mousewheelTimer );
			this.mousewheelTimer = this._delay( function() {
				if ( this.spinning ) {
					this._stop( event );
				}
			}, 100 );
			event.preventDefault();
		},
		"mousedown .ui-spinner-button": function( event ) {
			var previous;

			// We never want the buttons to have focus; whenever the user is
			// interacting with the spinner, the focus should be on the input.
			// If the input is focused then this.previous is properly set from
			// when the input first received focus. If the input is not focused
			// then we need to set this.previous based on the value before spinning.
			previous = this.element[ 0 ] === $.ui.safeActiveElement( this.document[ 0 ] ) ?
				this.previous : this.element.val();
			function checkFocus() {
				var isActive = this.element[ 0 ] === $.ui.safeActiveElement( this.document[ 0 ] );
				if ( !isActive ) {
					this.element.trigger( "focus" );
					this.previous = previous;

					// support: IE
					// IE sets focus asynchronously, so we need to check if focus
					// moved off of the input because the user clicked on the button.
					this._delay( function() {
						this.previous = previous;
					} );
				}
			}

			// Ensure focus is on (or stays on) the text field
			event.preventDefault();
			checkFocus.call( this );

			// Support: IE
			// IE doesn't prevent moving focus even with event.preventDefault()
			// so we set a flag to know when we should ignore the blur event
			// and check (again) if focus moved off of the input.
			this.cancelBlur = true;
			this._delay( function() {
				delete this.cancelBlur;
				checkFocus.call( this );
			} );

			if ( this._start( event ) === false ) {
				return;
			}

			this._repeat( null, $( event.currentTarget )
				.hasClass( "ui-spinner-up" ) ? 1 : -1, event );
		},
		"mouseup .ui-spinner-button": "_stop",
		"mouseenter .ui-spinner-button": function( event ) {

			// button will add ui-state-active if mouse was down while mouseleave and kept down
			if ( !$( event.currentTarget ).hasClass( "ui-state-active" ) ) {
				return;
			}

			if ( this._start( event ) === false ) {
				return false;
			}
			this._repeat( null, $( event.currentTarget )
				.hasClass( "ui-spinner-up" ) ? 1 : -1, event );
		},

		// TODO: do we really want to consider this a stop?
		// shouldn't we just stop the repeater and wait until mouseup before
		// we trigger the stop event?
		"mouseleave .ui-spinner-button": "_stop"
	},

	// Support mobile enhanced option and make backcompat more sane
	_enhance: function() {
		this.uiSpinner = this.element
			.attr( "autocomplete", "off" )
			.wrap( "<span>" )
			.parent()

				// Add buttons
				.append(
					"<a></a><a></a>"
				);
	},

	_draw: function() {
		this._enhance();

		this._addClass( this.uiSpinner, "ui-spinner", "ui-widget ui-widget-content" );
		this._addClass( "ui-spinner-input" );

		this.element.attr( "role", "spinbutton" );

		// Button bindings
		this.buttons = this.uiSpinner.children( "a" )
			.attr( "tabIndex", -1 )
			.attr( "aria-hidden", true )
			.button( {
				classes: {
					"ui-button": ""
				}
			} );

		// TODO: Right now button does not support classes this is already updated in button PR
		this._removeClass( this.buttons, "ui-corner-all" );

		this._addClass( this.buttons.first(), "ui-spinner-button ui-spinner-up" );
		this._addClass( this.buttons.last(), "ui-spinner-button ui-spinner-down" );
		this.buttons.first().button( {
			"icon": this.options.icons.up,
			"showLabel": false
		} );
		this.buttons.last().button( {
			"icon": this.options.icons.down,
			"showLabel": false
		} );

		// IE 6 doesn't understand height: 50% for the buttons
		// unless the wrapper has an explicit height
		if ( this.buttons.height() > Math.ceil( this.uiSpinner.height() * 0.5 ) &&
				this.uiSpinner.height() > 0 ) {
			this.uiSpinner.height( this.uiSpinner.height() );
		}
	},

	_keydown: function( event ) {
		var options = this.options,
			keyCode = $.ui.keyCode;

		switch ( event.keyCode ) {
		case keyCode.UP:
			this._repeat( null, 1, event );
			return true;
		case keyCode.DOWN:
			this._repeat( null, -1, event );
			return true;
		case keyCode.PAGE_UP:
			this._repeat( null, options.page, event );
			return true;
		case keyCode.PAGE_DOWN:
			this._repeat( null, -options.page, event );
			return true;
		}

		return false;
	},

	_start: function( event ) {
		if ( !this.spinning && this._trigger( "start", event ) === false ) {
			return false;
		}

		if ( !this.counter ) {
			this.counter = 1;
		}
		this.spinning = true;
		return true;
	},

	_repeat: function( i, steps, event ) {
		i = i || 500;

		clearTimeout( this.timer );
		this.timer = this._delay( function() {
			this._repeat( 40, steps, event );
		}, i );

		this._spin( steps * this.options.step, event );
	},

	_spin: function( step, event ) {
		var value = this.value() || 0;

		if ( !this.counter ) {
			this.counter = 1;
		}

		value = this._adjustValue( value + step * this._increment( this.counter ) );

		if ( !this.spinning || this._trigger( "spin", event, { value: value } ) !== false ) {
			this._value( value );
			this.counter++;
		}
	},

	_increment: function( i ) {
		var incremental = this.options.incremental;

		if ( incremental ) {
			return $.isFunction( incremental ) ?
				incremental( i ) :
				Math.floor( i * i * i / 50000 - i * i / 500 + 17 * i / 200 + 1 );
		}

		return 1;
	},

	_precision: function() {
		var precision = this._precisionOf( this.options.step );
		if ( this.options.min !== null ) {
			precision = Math.max( precision, this._precisionOf( this.options.min ) );
		}
		return precision;
	},

	_precisionOf: function( num ) {
		var str = num.toString(),
			decimal = str.indexOf( "." );
		return decimal === -1 ? 0 : str.length - decimal - 1;
	},

	_adjustValue: function( value ) {
		var base, aboveMin,
			options = this.options;

		// Make sure we're at a valid step
		// - find out where we are relative to the base (min or 0)
		base = options.min !== null ? options.min : 0;
		aboveMin = value - base;

		// - round to the nearest step
		aboveMin = Math.round( aboveMin / options.step ) * options.step;

		// - rounding is based on 0, so adjust back to our base
		value = base + aboveMin;

		// Fix precision from bad JS floating point math
		value = parseFloat( value.toFixed( this._precision() ) );

		// Clamp the value
		if ( options.max !== null && value > options.max ) {
			return options.max;
		}
		if ( options.min !== null && value < options.min ) {
			return options.min;
		}

		return value;
	},

	_stop: function( event ) {
		if ( !this.spinning ) {
			return;
		}

		clearTimeout( this.timer );
		clearTimeout( this.mousewheelTimer );
		this.counter = 0;
		this.spinning = false;
		this._trigger( "stop", event );
	},

	_setOption: function( key, value ) {
		var prevValue, first, last;

		if ( key === "culture" || key === "numberFormat" ) {
			prevValue = this._parse( this.element.val() );
			this.options[ key ] = value;
			this.element.val( this._format( prevValue ) );
			return;
		}

		if ( key === "max" || key === "min" || key === "step" ) {
			if ( typeof value === "string" ) {
				value = this._parse( value );
			}
		}
		if ( key === "icons" ) {
			first = this.buttons.first().find( ".ui-icon" );
			this._removeClass( first, null, this.options.icons.up );
			this._addClass( first, null, value.up );
			last = this.buttons.last().find( ".ui-icon" );
			this._removeClass( last, null, this.options.icons.down );
			this._addClass( last, null, value.down );
		}

		this._super( key, value );
	},

	_setOptionDisabled: function( value ) {
		this._super( value );

		this._toggleClass( this.uiSpinner, null, "ui-state-disabled", !!value );
		this.element.prop( "disabled", !!value );
		this.buttons.button( value ? "disable" : "enable" );
	},

	_setOptions: spinnerModifer( function( options ) {
		this._super( options );
	} ),

	_parse: function( val ) {
		if ( typeof val === "string" && val !== "" ) {
			val = window.Globalize && this.options.numberFormat ?
				Globalize.parseFloat( val, 10, this.options.culture ) : +val;
		}
		return val === "" || isNaN( val ) ? null : val;
	},

	_format: function( value ) {
		if ( value === "" ) {
			return "";
		}
		return window.Globalize && this.options.numberFormat ?
			Globalize.format( value, this.options.numberFormat, this.options.culture ) :
			value;
	},

	_refresh: function() {
		this.element.attr( {
			"aria-valuemin": this.options.min,
			"aria-valuemax": this.options.max,

			// TODO: what should we do with values that can't be parsed?
			"aria-valuenow": this._parse( this.element.val() )
		} );
	},

	isValid: function() {
		var value = this.value();

		// Null is invalid
		if ( value === null ) {
			return false;
		}

		// If value gets adjusted, it's invalid
		return value === this._adjustValue( value );
	},

	// Update the value without triggering change
	_value: function( value, allowAny ) {
		var parsed;
		if ( value !== "" ) {
			parsed = this._parse( value );
			if ( parsed !== null ) {
				if ( !allowAny ) {
					parsed = this._adjustValue( parsed );
				}
				value = this._format( parsed );
			}
		}
		this.element.val( value );
		this._refresh();
	},

	_destroy: function() {
		this.element
			.prop( "disabled", false )
			.removeAttr( "autocomplete role aria-valuemin aria-valuemax aria-valuenow" );

		this.uiSpinner.replaceWith( this.element );
	},

	stepUp: spinnerModifer( function( steps ) {
		this._stepUp( steps );
	} ),
	_stepUp: function( steps ) {
		if ( this._start() ) {
			this._spin( ( steps || 1 ) * this.options.step );
			this._stop();
		}
	},

	stepDown: spinnerModifer( function( steps ) {
		this._stepDown( steps );
	} ),
	_stepDown: function( steps ) {
		if ( this._start() ) {
			this._spin( ( steps || 1 ) * -this.options.step );
			this._stop();
		}
	},

	pageUp: spinnerModifer( function( pages ) {
		this._stepUp( ( pages || 1 ) * this.options.page );
	} ),

	pageDown: spinnerModifer( function( pages ) {
		this._stepDown( ( pages || 1 ) * this.options.page );
	} ),

	value: function( newVal ) {
		if ( !arguments.length ) {
			return this._parse( this.element.val() );
		}
		spinnerModifer( this._value ).call( this, newVal );
	},

	widget: function() {
		return this.uiSpinner;
	}
} );

// DEPRECATED
// TODO: switch return back to widget declaration at top of file when this is removed
if ( $.uiBackCompat !== false ) {

	// Backcompat for spinner html extension points
	$.widget( "ui.spinner", $.ui.spinner, {
		_enhance: function() {
			this.uiSpinner = this.element
				.attr( "autocomplete", "off" )
				.wrap( this._uiSpinnerHtml() )
				.parent()

					// Add buttons
					.append( this._buttonHtml() );
		},
		_uiSpinnerHtml: function() {
			return "<span>";
		},

		_buttonHtml: function() {
			return "<a></a><a></a>";
		}
	} );
}

var widgetsSpinner = $.ui.spinner;


/*!
 * jQuery UI Tabs 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Tabs
//>>group: Widgets
//>>description: Transforms a set of container elements into a tab structure.
//>>docs: http://api.jqueryui.com/tabs/
//>>demos: http://jqueryui.com/tabs/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/tabs.css
//>>css.theme: ../../themes/base/theme.css



$.widget( "ui.tabs", {
	version: "1.12.1",
	delay: 300,
	options: {
		active: null,
		classes: {
			"ui-tabs": "ui-corner-all",
			"ui-tabs-nav": "ui-corner-all",
			"ui-tabs-panel": "ui-corner-bottom",
			"ui-tabs-tab": "ui-corner-top"
		},
		collapsible: false,
		event: "click",
		heightStyle: "content",
		hide: null,
		show: null,

		// Callbacks
		activate: null,
		beforeActivate: null,
		beforeLoad: null,
		load: null
	},

	_isLocal: ( function() {
		var rhash = /#.*$/;

		return function( anchor ) {
			var anchorUrl, locationUrl;

			anchorUrl = anchor.href.replace( rhash, "" );
			locationUrl = location.href.replace( rhash, "" );

			// Decoding may throw an error if the URL isn't UTF-8 (#9518)
			try {
				anchorUrl = decodeURIComponent( anchorUrl );
			} catch ( error ) {}
			try {
				locationUrl = decodeURIComponent( locationUrl );
			} catch ( error ) {}

			return anchor.hash.length > 1 && anchorUrl === locationUrl;
		};
	} )(),

	_create: function() {
		var that = this,
			options = this.options;

		this.running = false;

		this._addClass( "ui-tabs", "ui-widget ui-widget-content" );
		this._toggleClass( "ui-tabs-collapsible", null, options.collapsible );

		this._processTabs();
		options.active = this._initialActive();

		// Take disabling tabs via class attribute from HTML
		// into account and update option properly.
		if ( $.isArray( options.disabled ) ) {
			options.disabled = $.unique( options.disabled.concat(
				$.map( this.tabs.filter( ".ui-state-disabled" ), function( li ) {
					return that.tabs.index( li );
				} )
			) ).sort();
		}

		// Check for length avoids error when initializing empty list
		if ( this.options.active !== false && this.anchors.length ) {
			this.active = this._findActive( options.active );
		} else {
			this.active = $();
		}

		this._refresh();

		if ( this.active.length ) {
			this.load( options.active );
		}
	},

	_initialActive: function() {
		var active = this.options.active,
			collapsible = this.options.collapsible,
			locationHash = location.hash.substring( 1 );

		if ( active === null ) {

			// check the fragment identifier in the URL
			if ( locationHash ) {
				this.tabs.each( function( i, tab ) {
					if ( $( tab ).attr( "aria-controls" ) === locationHash ) {
						active = i;
						return false;
					}
				} );
			}

			// Check for a tab marked active via a class
			if ( active === null ) {
				active = this.tabs.index( this.tabs.filter( ".ui-tabs-active" ) );
			}

			// No active tab, set to false
			if ( active === null || active === -1 ) {
				active = this.tabs.length ? 0 : false;
			}
		}

		// Handle numbers: negative, out of range
		if ( active !== false ) {
			active = this.tabs.index( this.tabs.eq( active ) );
			if ( active === -1 ) {
				active = collapsible ? false : 0;
			}
		}

		// Don't allow collapsible: false and active: false
		if ( !collapsible && active === false && this.anchors.length ) {
			active = 0;
		}

		return active;
	},

	_getCreateEventData: function() {
		return {
			tab: this.active,
			panel: !this.active.length ? $() : this._getPanelForTab( this.active )
		};
	},

	_tabKeydown: function( event ) {
		var focusedTab = $( $.ui.safeActiveElement( this.document[ 0 ] ) ).closest( "li" ),
			selectedIndex = this.tabs.index( focusedTab ),
			goingForward = true;

		if ( this._handlePageNav( event ) ) {
			return;
		}

		switch ( event.keyCode ) {
		case $.ui.keyCode.RIGHT:
		case $.ui.keyCode.DOWN:
			selectedIndex++;
			break;
		case $.ui.keyCode.UP:
		case $.ui.keyCode.LEFT:
			goingForward = false;
			selectedIndex--;
			break;
		case $.ui.keyCode.END:
			selectedIndex = this.anchors.length - 1;
			break;
		case $.ui.keyCode.HOME:
			selectedIndex = 0;
			break;
		case $.ui.keyCode.SPACE:

			// Activate only, no collapsing
			event.preventDefault();
			clearTimeout( this.activating );
			this._activate( selectedIndex );
			return;
		case $.ui.keyCode.ENTER:

			// Toggle (cancel delayed activation, allow collapsing)
			event.preventDefault();
			clearTimeout( this.activating );

			// Determine if we should collapse or activate
			this._activate( selectedIndex === this.options.active ? false : selectedIndex );
			return;
		default:
			return;
		}

		// Focus the appropriate tab, based on which key was pressed
		event.preventDefault();
		clearTimeout( this.activating );
		selectedIndex = this._focusNextTab( selectedIndex, goingForward );

		// Navigating with control/command key will prevent automatic activation
		if ( !event.ctrlKey && !event.metaKey ) {

			// Update aria-selected immediately so that AT think the tab is already selected.
			// Otherwise AT may confuse the user by stating that they need to activate the tab,
			// but the tab will already be activated by the time the announcement finishes.
			focusedTab.attr( "aria-selected", "false" );
			this.tabs.eq( selectedIndex ).attr( "aria-selected", "true" );

			this.activating = this._delay( function() {
				this.option( "active", selectedIndex );
			}, this.delay );
		}
	},

	_panelKeydown: function( event ) {
		if ( this._handlePageNav( event ) ) {
			return;
		}

		// Ctrl+up moves focus to the current tab
		if ( event.ctrlKey && event.keyCode === $.ui.keyCode.UP ) {
			event.preventDefault();
			this.active.trigger( "focus" );
		}
	},

	// Alt+page up/down moves focus to the previous/next tab (and activates)
	_handlePageNav: function( event ) {
		if ( event.altKey && event.keyCode === $.ui.keyCode.PAGE_UP ) {
			this._activate( this._focusNextTab( this.options.active - 1, false ) );
			return true;
		}
		if ( event.altKey && event.keyCode === $.ui.keyCode.PAGE_DOWN ) {
			this._activate( this._focusNextTab( this.options.active + 1, true ) );
			return true;
		}
	},

	_findNextTab: function( index, goingForward ) {
		var lastTabIndex = this.tabs.length - 1;

		function constrain() {
			if ( index > lastTabIndex ) {
				index = 0;
			}
			if ( index < 0 ) {
				index = lastTabIndex;
			}
			return index;
		}

		while ( $.inArray( constrain(), this.options.disabled ) !== -1 ) {
			index = goingForward ? index + 1 : index - 1;
		}

		return index;
	},

	_focusNextTab: function( index, goingForward ) {
		index = this._findNextTab( index, goingForward );
		this.tabs.eq( index ).trigger( "focus" );
		return index;
	},

	_setOption: function( key, value ) {
		if ( key === "active" ) {

			// _activate() will handle invalid values and update this.options
			this._activate( value );
			return;
		}

		this._super( key, value );

		if ( key === "collapsible" ) {
			this._toggleClass( "ui-tabs-collapsible", null, value );

			// Setting collapsible: false while collapsed; open first panel
			if ( !value && this.options.active === false ) {
				this._activate( 0 );
			}
		}

		if ( key === "event" ) {
			this._setupEvents( value );
		}

		if ( key === "heightStyle" ) {
			this._setupHeightStyle( value );
		}
	},

	_sanitizeSelector: function( hash ) {
		return hash ? hash.replace( /[!"$%&'()*+,.\/:;<=>?@\[\]\^`{|}~]/g, "\\$&" ) : "";
	},

	refresh: function() {
		var options = this.options,
			lis = this.tablist.children( ":has(a[href])" );

		// Get disabled tabs from class attribute from HTML
		// this will get converted to a boolean if needed in _refresh()
		options.disabled = $.map( lis.filter( ".ui-state-disabled" ), function( tab ) {
			return lis.index( tab );
		} );

		this._processTabs();

		// Was collapsed or no tabs
		if ( options.active === false || !this.anchors.length ) {
			options.active = false;
			this.active = $();

		// was active, but active tab is gone
		} else if ( this.active.length && !$.contains( this.tablist[ 0 ], this.active[ 0 ] ) ) {

			// all remaining tabs are disabled
			if ( this.tabs.length === options.disabled.length ) {
				options.active = false;
				this.active = $();

			// activate previous tab
			} else {
				this._activate( this._findNextTab( Math.max( 0, options.active - 1 ), false ) );
			}

		// was active, active tab still exists
		} else {

			// make sure active index is correct
			options.active = this.tabs.index( this.active );
		}

		this._refresh();
	},

	_refresh: function() {
		this._setOptionDisabled( this.options.disabled );
		this._setupEvents( this.options.event );
		this._setupHeightStyle( this.options.heightStyle );

		this.tabs.not( this.active ).attr( {
			"aria-selected": "false",
			"aria-expanded": "false",
			tabIndex: -1
		} );
		this.panels.not( this._getPanelForTab( this.active ) )
			.hide()
			.attr( {
				"aria-hidden": "true"
			} );

		// Make sure one tab is in the tab order
		if ( !this.active.length ) {
			this.tabs.eq( 0 ).attr( "tabIndex", 0 );
		} else {
			this.active
				.attr( {
					"aria-selected": "true",
					"aria-expanded": "true",
					tabIndex: 0
				} );
			this._addClass( this.active, "ui-tabs-active", "ui-state-active" );
			this._getPanelForTab( this.active )
				.show()
				.attr( {
					"aria-hidden": "false"
				} );
		}
	},

	_processTabs: function() {
		var that = this,
			prevTabs = this.tabs,
			prevAnchors = this.anchors,
			prevPanels = this.panels;

		this.tablist = this._getList().attr( "role", "tablist" );
		this._addClass( this.tablist, "ui-tabs-nav",
			"ui-helper-reset ui-helper-clearfix ui-widget-header" );

		// Prevent users from focusing disabled tabs via click
		this.tablist
			.on( "mousedown" + this.eventNamespace, "> li", function( event ) {
				if ( $( this ).is( ".ui-state-disabled" ) ) {
					event.preventDefault();
				}
			} )

			// Support: IE <9
			// Preventing the default action in mousedown doesn't prevent IE
			// from focusing the element, so if the anchor gets focused, blur.
			// We don't have to worry about focusing the previously focused
			// element since clicking on a non-focusable element should focus
			// the body anyway.
			.on( "focus" + this.eventNamespace, ".ui-tabs-anchor", function() {
				if ( $( this ).closest( "li" ).is( ".ui-state-disabled" ) ) {
					this.blur();
				}
			} );

		this.tabs = this.tablist.find( "> li:has(a[href])" )
			.attr( {
				role: "tab",
				tabIndex: -1
			} );
		this._addClass( this.tabs, "ui-tabs-tab", "ui-state-default" );

		this.anchors = this.tabs.map( function() {
			return $( "a", this )[ 0 ];
		} )
			.attr( {
				role: "presentation",
				tabIndex: -1
			} );
		this._addClass( this.anchors, "ui-tabs-anchor" );

		this.panels = $();

		this.anchors.each( function( i, anchor ) {
			var selector, panel, panelId,
				anchorId = $( anchor ).uniqueId().attr( "id" ),
				tab = $( anchor ).closest( "li" ),
				originalAriaControls = tab.attr( "aria-controls" );

			// Inline tab
			if ( that._isLocal( anchor ) ) {
				selector = anchor.hash;
				panelId = selector.substring( 1 );
				panel = that.element.find( that._sanitizeSelector( selector ) );

			// remote tab
			} else {

				// If the tab doesn't already have aria-controls,
				// generate an id by using a throw-away element
				panelId = tab.attr( "aria-controls" ) || $( {} ).uniqueId()[ 0 ].id;
				selector = "#" + panelId;
				panel = that.element.find( selector );
				if ( !panel.length ) {
					panel = that._createPanel( panelId );
					panel.insertAfter( that.panels[ i - 1 ] || that.tablist );
				}
				panel.attr( "aria-live", "polite" );
			}

			if ( panel.length ) {
				that.panels = that.panels.add( panel );
			}
			if ( originalAriaControls ) {
				tab.data( "ui-tabs-aria-controls", originalAriaControls );
			}
			tab.attr( {
				"aria-controls": panelId,
				"aria-labelledby": anchorId
			} );
			panel.attr( "aria-labelledby", anchorId );
		} );

		this.panels.attr( "role", "tabpanel" );
		this._addClass( this.panels, "ui-tabs-panel", "ui-widget-content" );

		// Avoid memory leaks (#10056)
		if ( prevTabs ) {
			this._off( prevTabs.not( this.tabs ) );
			this._off( prevAnchors.not( this.anchors ) );
			this._off( prevPanels.not( this.panels ) );
		}
	},

	// Allow overriding how to find the list for rare usage scenarios (#7715)
	_getList: function() {
		return this.tablist || this.element.find( "ol, ul" ).eq( 0 );
	},

	_createPanel: function( id ) {
		return $( "<div>" )
			.attr( "id", id )
			.data( "ui-tabs-destroy", true );
	},

	_setOptionDisabled: function( disabled ) {
		var currentItem, li, i;

		if ( $.isArray( disabled ) ) {
			if ( !disabled.length ) {
				disabled = false;
			} else if ( disabled.length === this.anchors.length ) {
				disabled = true;
			}
		}

		// Disable tabs
		for ( i = 0; ( li = this.tabs[ i ] ); i++ ) {
			currentItem = $( li );
			if ( disabled === true || $.inArray( i, disabled ) !== -1 ) {
				currentItem.attr( "aria-disabled", "true" );
				this._addClass( currentItem, null, "ui-state-disabled" );
			} else {
				currentItem.removeAttr( "aria-disabled" );
				this._removeClass( currentItem, null, "ui-state-disabled" );
			}
		}

		this.options.disabled = disabled;

		this._toggleClass( this.widget(), this.widgetFullName + "-disabled", null,
			disabled === true );
	},

	_setupEvents: function( event ) {
		var events = {};
		if ( event ) {
			$.each( event.split( " " ), function( index, eventName ) {
				events[ eventName ] = "_eventHandler";
			} );
		}

		this._off( this.anchors.add( this.tabs ).add( this.panels ) );

		// Always prevent the default action, even when disabled
		this._on( true, this.anchors, {
			click: function( event ) {
				event.preventDefault();
			}
		} );
		this._on( this.anchors, events );
		this._on( this.tabs, { keydown: "_tabKeydown" } );
		this._on( this.panels, { keydown: "_panelKeydown" } );

		this._focusable( this.tabs );
		this._hoverable( this.tabs );
	},

	_setupHeightStyle: function( heightStyle ) {
		var maxHeight,
			parent = this.element.parent();

		if ( heightStyle === "fill" ) {
			maxHeight = parent.height();
			maxHeight -= this.element.outerHeight() - this.element.height();

			this.element.siblings( ":visible" ).each( function() {
				var elem = $( this ),
					position = elem.css( "position" );

				if ( position === "absolute" || position === "fixed" ) {
					return;
				}
				maxHeight -= elem.outerHeight( true );
			} );

			this.element.children().not( this.panels ).each( function() {
				maxHeight -= $( this ).outerHeight( true );
			} );

			this.panels.each( function() {
				$( this ).height( Math.max( 0, maxHeight -
					$( this ).innerHeight() + $( this ).height() ) );
			} )
				.css( "overflow", "auto" );
		} else if ( heightStyle === "auto" ) {
			maxHeight = 0;
			this.panels.each( function() {
				maxHeight = Math.max( maxHeight, $( this ).height( "" ).height() );
			} ).height( maxHeight );
		}
	},

	_eventHandler: function( event ) {
		var options = this.options,
			active = this.active,
			anchor = $( event.currentTarget ),
			tab = anchor.closest( "li" ),
			clickedIsActive = tab[ 0 ] === active[ 0 ],
			collapsing = clickedIsActive && options.collapsible,
			toShow = collapsing ? $() : this._getPanelForTab( tab ),
			toHide = !active.length ? $() : this._getPanelForTab( active ),
			eventData = {
				oldTab: active,
				oldPanel: toHide,
				newTab: collapsing ? $() : tab,
				newPanel: toShow
			};

		event.preventDefault();

		if ( tab.hasClass( "ui-state-disabled" ) ||

				// tab is already loading
				tab.hasClass( "ui-tabs-loading" ) ||

				// can't switch durning an animation
				this.running ||

				// click on active header, but not collapsible
				( clickedIsActive && !options.collapsible ) ||

				// allow canceling activation
				( this._trigger( "beforeActivate", event, eventData ) === false ) ) {
			return;
		}

		options.active = collapsing ? false : this.tabs.index( tab );

		this.active = clickedIsActive ? $() : tab;
		if ( this.xhr ) {
			this.xhr.abort();
		}

		if ( !toHide.length && !toShow.length ) {
			$.error( "jQuery UI Tabs: Mismatching fragment identifier." );
		}

		if ( toShow.length ) {
			this.load( this.tabs.index( tab ), event );
		}
		this._toggle( event, eventData );
	},

	// Handles show/hide for selecting tabs
	_toggle: function( event, eventData ) {
		var that = this,
			toShow = eventData.newPanel,
			toHide = eventData.oldPanel;

		this.running = true;

		function complete() {
			that.running = false;
			that._trigger( "activate", event, eventData );
		}

		function show() {
			that._addClass( eventData.newTab.closest( "li" ), "ui-tabs-active", "ui-state-active" );

			if ( toShow.length && that.options.show ) {
				that._show( toShow, that.options.show, complete );
			} else {
				toShow.show();
				complete();
			}
		}

		// Start out by hiding, then showing, then completing
		if ( toHide.length && this.options.hide ) {
			this._hide( toHide, this.options.hide, function() {
				that._removeClass( eventData.oldTab.closest( "li" ),
					"ui-tabs-active", "ui-state-active" );
				show();
			} );
		} else {
			this._removeClass( eventData.oldTab.closest( "li" ),
				"ui-tabs-active", "ui-state-active" );
			toHide.hide();
			show();
		}

		toHide.attr( "aria-hidden", "true" );
		eventData.oldTab.attr( {
			"aria-selected": "false",
			"aria-expanded": "false"
		} );

		// If we're switching tabs, remove the old tab from the tab order.
		// If we're opening from collapsed state, remove the previous tab from the tab order.
		// If we're collapsing, then keep the collapsing tab in the tab order.
		if ( toShow.length && toHide.length ) {
			eventData.oldTab.attr( "tabIndex", -1 );
		} else if ( toShow.length ) {
			this.tabs.filter( function() {
				return $( this ).attr( "tabIndex" ) === 0;
			} )
				.attr( "tabIndex", -1 );
		}

		toShow.attr( "aria-hidden", "false" );
		eventData.newTab.attr( {
			"aria-selected": "true",
			"aria-expanded": "true",
			tabIndex: 0
		} );
	},

	_activate: function( index ) {
		var anchor,
			active = this._findActive( index );

		// Trying to activate the already active panel
		if ( active[ 0 ] === this.active[ 0 ] ) {
			return;
		}

		// Trying to collapse, simulate a click on the current active header
		if ( !active.length ) {
			active = this.active;
		}

		anchor = active.find( ".ui-tabs-anchor" )[ 0 ];
		this._eventHandler( {
			target: anchor,
			currentTarget: anchor,
			preventDefault: $.noop
		} );
	},

	_findActive: function( index ) {
		return index === false ? $() : this.tabs.eq( index );
	},

	_getIndex: function( index ) {

		// meta-function to give users option to provide a href string instead of a numerical index.
		if ( typeof index === "string" ) {
			index = this.anchors.index( this.anchors.filter( "[href$='" +
				$.ui.escapeSelector( index ) + "']" ) );
		}

		return index;
	},

	_destroy: function() {
		if ( this.xhr ) {
			this.xhr.abort();
		}

		this.tablist
			.removeAttr( "role" )
			.off( this.eventNamespace );

		this.anchors
			.removeAttr( "role tabIndex" )
			.removeUniqueId();

		this.tabs.add( this.panels ).each( function() {
			if ( $.data( this, "ui-tabs-destroy" ) ) {
				$( this ).remove();
			} else {
				$( this ).removeAttr( "role tabIndex " +
					"aria-live aria-busy aria-selected aria-labelledby aria-hidden aria-expanded" );
			}
		} );

		this.tabs.each( function() {
			var li = $( this ),
				prev = li.data( "ui-tabs-aria-controls" );
			if ( prev ) {
				li
					.attr( "aria-controls", prev )
					.removeData( "ui-tabs-aria-controls" );
			} else {
				li.removeAttr( "aria-controls" );
			}
		} );

		this.panels.show();

		if ( this.options.heightStyle !== "content" ) {
			this.panels.css( "height", "" );
		}
	},

	enable: function( index ) {
		var disabled = this.options.disabled;
		if ( disabled === false ) {
			return;
		}

		if ( index === undefined ) {
			disabled = false;
		} else {
			index = this._getIndex( index );
			if ( $.isArray( disabled ) ) {
				disabled = $.map( disabled, function( num ) {
					return num !== index ? num : null;
				} );
			} else {
				disabled = $.map( this.tabs, function( li, num ) {
					return num !== index ? num : null;
				} );
			}
		}
		this._setOptionDisabled( disabled );
	},

	disable: function( index ) {
		var disabled = this.options.disabled;
		if ( disabled === true ) {
			return;
		}

		if ( index === undefined ) {
			disabled = true;
		} else {
			index = this._getIndex( index );
			if ( $.inArray( index, disabled ) !== -1 ) {
				return;
			}
			if ( $.isArray( disabled ) ) {
				disabled = $.merge( [ index ], disabled ).sort();
			} else {
				disabled = [ index ];
			}
		}
		this._setOptionDisabled( disabled );
	},

	load: function( index, event ) {
		index = this._getIndex( index );
		var that = this,
			tab = this.tabs.eq( index ),
			anchor = tab.find( ".ui-tabs-anchor" ),
			panel = this._getPanelForTab( tab ),
			eventData = {
				tab: tab,
				panel: panel
			},
			complete = function( jqXHR, status ) {
				if ( status === "abort" ) {
					that.panels.stop( false, true );
				}

				that._removeClass( tab, "ui-tabs-loading" );
				panel.removeAttr( "aria-busy" );

				if ( jqXHR === that.xhr ) {
					delete that.xhr;
				}
			};

		// Not remote
		if ( this._isLocal( anchor[ 0 ] ) ) {
			return;
		}

		this.xhr = $.ajax( this._ajaxSettings( anchor, event, eventData ) );

		// Support: jQuery <1.8
		// jQuery <1.8 returns false if the request is canceled in beforeSend,
		// but as of 1.8, $.ajax() always returns a jqXHR object.
		if ( this.xhr && this.xhr.statusText !== "canceled" ) {
			this._addClass( tab, "ui-tabs-loading" );
			panel.attr( "aria-busy", "true" );

			this.xhr
				.done( function( response, status, jqXHR ) {

					// support: jQuery <1.8
					// http://bugs.jquery.com/ticket/11778
					setTimeout( function() {
						panel.html( response );
						that._trigger( "load", event, eventData );

						complete( jqXHR, status );
					}, 1 );
				} )
				.fail( function( jqXHR, status ) {

					// support: jQuery <1.8
					// http://bugs.jquery.com/ticket/11778
					setTimeout( function() {
						complete( jqXHR, status );
					}, 1 );
				} );
		}
	},

	_ajaxSettings: function( anchor, event, eventData ) {
		var that = this;
		return {

			// Support: IE <11 only
			// Strip any hash that exists to prevent errors with the Ajax request
			url: anchor.attr( "href" ).replace( /#.*$/, "" ),
			beforeSend: function( jqXHR, settings ) {
				return that._trigger( "beforeLoad", event,
					$.extend( { jqXHR: jqXHR, ajaxSettings: settings }, eventData ) );
			}
		};
	},

	_getPanelForTab: function( tab ) {
		var id = $( tab ).attr( "aria-controls" );
		return this.element.find( this._sanitizeSelector( "#" + id ) );
	}
} );

// DEPRECATED
// TODO: Switch return back to widget declaration at top of file when this is removed
if ( $.uiBackCompat !== false ) {

	// Backcompat for ui-tab class (now ui-tabs-tab)
	$.widget( "ui.tabs", $.ui.tabs, {
		_processTabs: function() {
			this._superApply( arguments );
			this._addClass( this.tabs, "ui-tab" );
		}
	} );
}

var widgetsTabs = $.ui.tabs;


/*!
 * jQuery UI Tooltip 1.12.1
 * http://jqueryui.com
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */

//>>label: Tooltip
//>>group: Widgets
//>>description: Shows additional information for any element on hover or focus.
//>>docs: http://api.jqueryui.com/tooltip/
//>>demos: http://jqueryui.com/tooltip/
//>>css.structure: ../../themes/base/core.css
//>>css.structure: ../../themes/base/tooltip.css
//>>css.theme: ../../themes/base/theme.css



$.widget( "ui.tooltip", {
	version: "1.12.1",
	options: {
		classes: {
			"ui-tooltip": "ui-corner-all ui-widget-shadow"
		},
		content: function() {

			// support: IE<9, Opera in jQuery <1.7
			// .text() can't accept undefined, so coerce to a string
			var title = $( this ).attr( "title" ) || "";

			// Escape title, since we're going from an attribute to raw HTML
			return $( "<a>" ).text( title ).html();
		},
		hide: true,

		// Disabled elements have inconsistent behavior across browsers (#8661)
		items: "[title]:not([disabled])",
		position: {
			my: "left top+15",
			at: "left bottom",
			collision: "flipfit flip"
		},
		show: true,
		track: false,

		// Callbacks
		close: null,
		open: null
	},

	_addDescribedBy: function( elem, id ) {
		var describedby = ( elem.attr( "aria-describedby" ) || "" ).split( /\s+/ );
		describedby.push( id );
		elem
			.data( "ui-tooltip-id", id )
			.attr( "aria-describedby", $.trim( describedby.join( " " ) ) );
	},

	_removeDescribedBy: function( elem ) {
		var id = elem.data( "ui-tooltip-id" ),
			describedby = ( elem.attr( "aria-describedby" ) || "" ).split( /\s+/ ),
			index = $.inArray( id, describedby );

		if ( index !== -1 ) {
			describedby.splice( index, 1 );
		}

		elem.removeData( "ui-tooltip-id" );
		describedby = $.trim( describedby.join( " " ) );
		if ( describedby ) {
			elem.attr( "aria-describedby", describedby );
		} else {
			elem.removeAttr( "aria-describedby" );
		}
	},

	_create: function() {
		this._on( {
			mouseover: "open",
			focusin: "open"
		} );

		// IDs of generated tooltips, needed for destroy
		this.tooltips = {};

		// IDs of parent tooltips where we removed the title attribute
		this.parents = {};

		// Append the aria-live region so tooltips announce correctly
		this.liveRegion = $( "<div>" )
			.attr( {
				role: "log",
				"aria-live": "assertive",
				"aria-relevant": "additions"
			} )
			.appendTo( this.document[ 0 ].body );
		this._addClass( this.liveRegion, null, "ui-helper-hidden-accessible" );

		this.disabledTitles = $( [] );
	},

	_setOption: function( key, value ) {
		var that = this;

		this._super( key, value );

		if ( key === "content" ) {
			$.each( this.tooltips, function( id, tooltipData ) {
				that._updateContent( tooltipData.element );
			} );
		}
	},

	_setOptionDisabled: function( value ) {
		this[ value ? "_disable" : "_enable" ]();
	},

	_disable: function() {
		var that = this;

		// Close open tooltips
		$.each( this.tooltips, function( id, tooltipData ) {
			var event = $.Event( "blur" );
			event.target = event.currentTarget = tooltipData.element[ 0 ];
			that.close( event, true );
		} );

		// Remove title attributes to prevent native tooltips
		this.disabledTitles = this.disabledTitles.add(
			this.element.find( this.options.items ).addBack()
				.filter( function() {
					var element = $( this );
					if ( element.is( "[title]" ) ) {
						return element
							.data( "ui-tooltip-title", element.attr( "title" ) )
							.removeAttr( "title" );
					}
				} )
		);
	},

	_enable: function() {

		// restore title attributes
		this.disabledTitles.each( function() {
			var element = $( this );
			if ( element.data( "ui-tooltip-title" ) ) {
				element.attr( "title", element.data( "ui-tooltip-title" ) );
			}
		} );
		this.disabledTitles = $( [] );
	},

	open: function( event ) {
		var that = this,
			target = $( event ? event.target : this.element )

				// we need closest here due to mouseover bubbling,
				// but always pointing at the same event target
				.closest( this.options.items );

		// No element to show a tooltip for or the tooltip is already open
		if ( !target.length || target.data( "ui-tooltip-id" ) ) {
			return;
		}

		if ( target.attr( "title" ) ) {
			target.data( "ui-tooltip-title", target.attr( "title" ) );
		}

		target.data( "ui-tooltip-open", true );

		// Kill parent tooltips, custom or native, for hover
		if ( event && event.type === "mouseover" ) {
			target.parents().each( function() {
				var parent = $( this ),
					blurEvent;
				if ( parent.data( "ui-tooltip-open" ) ) {
					blurEvent = $.Event( "blur" );
					blurEvent.target = blurEvent.currentTarget = this;
					that.close( blurEvent, true );
				}
				if ( parent.attr( "title" ) ) {
					parent.uniqueId();
					that.parents[ this.id ] = {
						element: this,
						title: parent.attr( "title" )
					};
					parent.attr( "title", "" );
				}
			} );
		}

		this._registerCloseHandlers( event, target );
		this._updateContent( target, event );
	},

	_updateContent: function( target, event ) {
		var content,
			contentOption = this.options.content,
			that = this,
			eventType = event ? event.type : null;

		if ( typeof contentOption === "string" || contentOption.nodeType ||
				contentOption.jquery ) {
			return this._open( event, target, contentOption );
		}

		content = contentOption.call( target[ 0 ], function( response ) {

			// IE may instantly serve a cached response for ajax requests
			// delay this call to _open so the other call to _open runs first
			that._delay( function() {

				// Ignore async response if tooltip was closed already
				if ( !target.data( "ui-tooltip-open" ) ) {
					return;
				}

				// JQuery creates a special event for focusin when it doesn't
				// exist natively. To improve performance, the native event
				// object is reused and the type is changed. Therefore, we can't
				// rely on the type being correct after the event finished
				// bubbling, so we set it back to the previous value. (#8740)
				if ( event ) {
					event.type = eventType;
				}
				this._open( event, target, response );
			} );
		} );
		if ( content ) {
			this._open( event, target, content );
		}
	},

	_open: function( event, target, content ) {
		var tooltipData, tooltip, delayedShow, a11yContent,
			positionOption = $.extend( {}, this.options.position );

		if ( !content ) {
			return;
		}

		// Content can be updated multiple times. If the tooltip already
		// exists, then just update the content and bail.
		tooltipData = this._find( target );
		if ( tooltipData ) {
			tooltipData.tooltip.find( ".ui-tooltip-content" ).html( content );
			return;
		}

		// If we have a title, clear it to prevent the native tooltip
		// we have to check first to avoid defining a title if none exists
		// (we don't want to cause an element to start matching [title])
		//
		// We use removeAttr only for key events, to allow IE to export the correct
		// accessible attributes. For mouse events, set to empty string to avoid
		// native tooltip showing up (happens only when removing inside mouseover).
		if ( target.is( "[title]" ) ) {
			if ( event && event.type === "mouseover" ) {
				target.attr( "title", "" );
			} else {
				target.removeAttr( "title" );
			}
		}

		tooltipData = this._tooltip( target );
		tooltip = tooltipData.tooltip;
		this._addDescribedBy( target, tooltip.attr( "id" ) );
		tooltip.find( ".ui-tooltip-content" ).html( content );

		// Support: Voiceover on OS X, JAWS on IE <= 9
		// JAWS announces deletions even when aria-relevant="additions"
		// Voiceover will sometimes re-read the entire log region's contents from the beginning
		this.liveRegion.children().hide();
		a11yContent = $( "<div>" ).html( tooltip.find( ".ui-tooltip-content" ).html() );
		a11yContent.removeAttr( "name" ).find( "[name]" ).removeAttr( "name" );
		a11yContent.removeAttr( "id" ).find( "[id]" ).removeAttr( "id" );
		a11yContent.appendTo( this.liveRegion );

		function position( event ) {
			positionOption.of = event;
			if ( tooltip.is( ":hidden" ) ) {
				return;
			}
			tooltip.position( positionOption );
		}
		if ( this.options.track && event && /^mouse/.test( event.type ) ) {
			this._on( this.document, {
				mousemove: position
			} );

			// trigger once to override element-relative positioning
			position( event );
		} else {
			tooltip.position( $.extend( {
				of: target
			}, this.options.position ) );
		}

		tooltip.hide();

		this._show( tooltip, this.options.show );

		// Handle tracking tooltips that are shown with a delay (#8644). As soon
		// as the tooltip is visible, position the tooltip using the most recent
		// event.
		// Adds the check to add the timers only when both delay and track options are set (#14682)
		if ( this.options.track && this.options.show && this.options.show.delay ) {
			delayedShow = this.delayedShow = setInterval( function() {
				if ( tooltip.is( ":visible" ) ) {
					position( positionOption.of );
					clearInterval( delayedShow );
				}
			}, $.fx.interval );
		}

		this._trigger( "open", event, { tooltip: tooltip } );
	},

	_registerCloseHandlers: function( event, target ) {
		var events = {
			keyup: function( event ) {
				if ( event.keyCode === $.ui.keyCode.ESCAPE ) {
					var fakeEvent = $.Event( event );
					fakeEvent.currentTarget = target[ 0 ];
					this.close( fakeEvent, true );
				}
			}
		};

		// Only bind remove handler for delegated targets. Non-delegated
		// tooltips will handle this in destroy.
		if ( target[ 0 ] !== this.element[ 0 ] ) {
			events.remove = function() {
				this._removeTooltip( this._find( target ).tooltip );
			};
		}

		if ( !event || event.type === "mouseover" ) {
			events.mouseleave = "close";
		}
		if ( !event || event.type === "focusin" ) {
			events.focusout = "close";
		}
		this._on( true, target, events );
	},

	close: function( event ) {
		var tooltip,
			that = this,
			target = $( event ? event.currentTarget : this.element ),
			tooltipData = this._find( target );

		// The tooltip may already be closed
		if ( !tooltipData ) {

			// We set ui-tooltip-open immediately upon open (in open()), but only set the
			// additional data once there's actually content to show (in _open()). So even if the
			// tooltip doesn't have full data, we always remove ui-tooltip-open in case we're in
			// the period between open() and _open().
			target.removeData( "ui-tooltip-open" );
			return;
		}

		tooltip = tooltipData.tooltip;

		// Disabling closes the tooltip, so we need to track when we're closing
		// to avoid an infinite loop in case the tooltip becomes disabled on close
		if ( tooltipData.closing ) {
			return;
		}

		// Clear the interval for delayed tracking tooltips
		clearInterval( this.delayedShow );

		// Only set title if we had one before (see comment in _open())
		// If the title attribute has changed since open(), don't restore
		if ( target.data( "ui-tooltip-title" ) && !target.attr( "title" ) ) {
			target.attr( "title", target.data( "ui-tooltip-title" ) );
		}

		this._removeDescribedBy( target );

		tooltipData.hiding = true;
		tooltip.stop( true );
		this._hide( tooltip, this.options.hide, function() {
			that._removeTooltip( $( this ) );
		} );

		target.removeData( "ui-tooltip-open" );
		this._off( target, "mouseleave focusout keyup" );

		// Remove 'remove' binding only on delegated targets
		if ( target[ 0 ] !== this.element[ 0 ] ) {
			this._off( target, "remove" );
		}
		this._off( this.document, "mousemove" );

		if ( event && event.type === "mouseleave" ) {
			$.each( this.parents, function( id, parent ) {
				$( parent.element ).attr( "title", parent.title );
				delete that.parents[ id ];
			} );
		}

		tooltipData.closing = true;
		this._trigger( "close", event, { tooltip: tooltip } );
		if ( !tooltipData.hiding ) {
			tooltipData.closing = false;
		}
	},

	_tooltip: function( element ) {
		var tooltip = $( "<div>" ).attr( "role", "tooltip" ),
			content = $( "<div>" ).appendTo( tooltip ),
			id = tooltip.uniqueId().attr( "id" );

		this._addClass( content, "ui-tooltip-content" );
		this._addClass( tooltip, "ui-tooltip", "ui-widget ui-widget-content" );

		tooltip.appendTo( this._appendTo( element ) );

		return this.tooltips[ id ] = {
			element: element,
			tooltip: tooltip
		};
	},

	_find: function( target ) {
		var id = target.data( "ui-tooltip-id" );
		return id ? this.tooltips[ id ] : null;
	},

	_removeTooltip: function( tooltip ) {
		tooltip.remove();
		delete this.tooltips[ tooltip.attr( "id" ) ];
	},

	_appendTo: function( target ) {
		var element = target.closest( ".ui-front, dialog" );

		if ( !element.length ) {
			element = this.document[ 0 ].body;
		}

		return element;
	},

	_destroy: function() {
		var that = this;

		// Close open tooltips
		$.each( this.tooltips, function( id, tooltipData ) {

			// Delegate to close method to handle common cleanup
			var event = $.Event( "blur" ),
				element = tooltipData.element;
			event.target = event.currentTarget = element[ 0 ];
			that.close( event, true );

			// Remove immediately; destroying an open tooltip doesn't use the
			// hide animation
			$( "#" + id ).remove();

			// Restore the title
			if ( element.data( "ui-tooltip-title" ) ) {

				// If the title attribute has changed since open(), don't restore
				if ( !element.attr( "title" ) ) {
					element.attr( "title", element.data( "ui-tooltip-title" ) );
				}
				element.removeData( "ui-tooltip-title" );
			}
		} );
		this.liveRegion.remove();
	}
} );

// DEPRECATED
// TODO: Switch return back to widget declaration at top of file when this is removed
if ( $.uiBackCompat !== false ) {

	// Backcompat for tooltipClass option
	$.widget( "ui.tooltip", $.ui.tooltip, {
		options: {
			tooltipClass: null
		},
		_tooltip: function() {
			var tooltipData = this._superApply( arguments );
			if ( this.options.tooltipClass ) {
				tooltipData.tooltip.addClass( this.options.tooltipClass );
			}
			return tooltipData;
		}
	} );
}

var widgetsTooltip = $.ui.tooltip;




}));
/**
 * angular-ui-sortable - This directive allows you to jQueryUI Sortable.
 * @version v0.19.0 - 2018-01-14
 * @link http://angular-ui.github.com
 * @license MIT
 */

(function(window, angular, undefined) {
'use strict';
/*
 jQuery UI Sortable plugin wrapper

 @param [ui-sortable] {object} Options to pass to $.fn.sortable() merged onto ui.config
 */
angular
  .module('ui.sortable', [])
  .value('uiSortableConfig', {
    // the default for jquery-ui sortable is "> *", we need to restrict this to
    // ng-repeat items
    // if the user uses
    items: '> [ng-repeat],> [data-ng-repeat],> [x-ng-repeat]'
  })
  .directive('uiSortable', [
    'uiSortableConfig',
    '$timeout',
    '$log',
    function(uiSortableConfig, $timeout, $log) {
      return {
        require: '?ngModel',
        scope: {
          ngModel: '=',
          uiSortable: '=',
          ////Expression bindings from html.
          create: '&uiSortableCreate',
          // helper:'&uiSortableHelper',
          start: '&uiSortableStart',
          activate: '&uiSortableActivate',
          // sort:'&uiSortableSort',
          // change:'&uiSortableChange',
          // over:'&uiSortableOver',
          // out:'&uiSortableOut',
          beforeStop: '&uiSortableBeforeStop',
          update: '&uiSortableUpdate',
          remove: '&uiSortableRemove',
          receive: '&uiSortableReceive',
          deactivate: '&uiSortableDeactivate',
          stop: '&uiSortableStop'
        },
        link: function(scope, element, attrs, ngModel) {
          var savedNodes;
          var helper;

          function combineCallbacks(first, second) {
            var firstIsFunc = typeof first === 'function';
            var secondIsFunc = typeof second === 'function';
            if (firstIsFunc && secondIsFunc) {
              return function() {
                first.apply(this, arguments);
                second.apply(this, arguments);
              };
            } else if (secondIsFunc) {
              return second;
            }
            return first;
          }

          function getSortableWidgetInstance(element) {
            // this is a fix to support jquery-ui prior to v1.11.x
            // otherwise we should be using `element.sortable('instance')`
            var data = element.data('ui-sortable');
            if (
              data &&
              typeof data === 'object' &&
              data.widgetFullName === 'ui-sortable'
            ) {
              return data;
            }
            return null;
          }

          function setItemChildrenWidth(item) {
            item.children().each(function() {
              var $el = angular.element(this);

              // Preserve the with of the element
              $el.width($el.width());
            });
          }

          function dummyHelper(e, item) {
            return item;
          }

          function patchSortableOption(key, value) {
            if (callbacks[key]) {
              if (key === 'stop') {
                // call apply after stop
                value = combineCallbacks(value, function() {
                  scope.$apply();
                });

                value = combineCallbacks(value, afterStop);
              }
              // wrap the callback
              value = combineCallbacks(callbacks[key], value);
            } else if (wrappers[key]) {
              value = wrappers[key](value);
            }

            // patch the options that need to have values set
            if (!value && (key === 'items' || key === 'ui-model-items')) {
              value = uiSortableConfig.items;
            }

            return value;
          }

          function patchUISortableOptions(
            newOpts,
            oldOpts,
            sortableWidgetInstance
          ) {
            function addDummyOptionKey(value, key) {
              if (!(key in opts)) {
                // add the key in the opts object so that
                // the patch function detects and handles it
                opts[key] = null;
              }
            }
            // for this directive to work we have to attach some callbacks
            angular.forEach(callbacks, addDummyOptionKey);

            // only initialize it in case we have to
            // update some options of the sortable
            var optsDiff = null;

            if (oldOpts) {
              // reset deleted options to default
              var defaultOptions;
              angular.forEach(oldOpts, function(oldValue, key) {
                if (!newOpts || !(key in newOpts)) {
                  if (key in directiveOpts) {
                    if (key === 'ui-floating') {
                      opts[key] = 'auto';
                    } else {
                      opts[key] = patchSortableOption(key, undefined);
                    }
                    return;
                  }

                  if (!defaultOptions) {
                    defaultOptions = angular.element.ui.sortable().options;
                  }
                  var defaultValue = defaultOptions[key];
                  defaultValue = patchSortableOption(key, defaultValue);

                  if (!optsDiff) {
                    optsDiff = {};
                  }
                  optsDiff[key] = defaultValue;
                  opts[key] = defaultValue;
                }
              });
            }

            newOpts = angular.extend({}, newOpts);
            // update changed options
            // handle the custom option of the directive first
            angular.forEach(newOpts, function(value, key) {
              if (key in directiveOpts) {
                if (
                  key === 'ui-floating' &&
                  (value === false || value === true) &&
                  sortableWidgetInstance
                ) {
                  sortableWidgetInstance.floating = value;
                }

                if (
                  key === 'ui-preserve-size' &&
                  (value === false || value === true)
                ) {
                  var userProvidedHelper = opts.helper;
                  newOpts.helper = function(e, item) {
                    if (opts['ui-preserve-size'] === true) {
                      setItemChildrenWidth(item);
                    }
                    return (userProvidedHelper || dummyHelper).apply(
                      this,
                      arguments
                    );
                  };
                }

                opts[key] = patchSortableOption(key, value);
              }
            });

            // handle the normal option of the directive
            angular.forEach(newOpts, function(value, key) {
              if (key in directiveOpts) {
                // the custom option of the directive are already handled
                return;
              }

              value = patchSortableOption(key, value);

              if (!optsDiff) {
                optsDiff = {};
              }
              optsDiff[key] = value;
              opts[key] = value;
            });

            return optsDiff;
          }

          function getPlaceholderElement(element) {
            var placeholder = element.sortable('option', 'placeholder');

            // placeholder.element will be a function if the placeholder, has
            // been created (placeholder will be an object).  If it hasn't
            // been created, either placeholder will be false if no
            // placeholder class was given or placeholder.element will be
            // undefined if a class was given (placeholder will be a string)
            if (
              placeholder &&
              placeholder.element &&
              typeof placeholder.element === 'function'
            ) {
              var result = placeholder.element();
              // workaround for jquery ui 1.9.x,
              // not returning jquery collection
              result = angular.element(result);
              return result;
            }
            return null;
          }

          function getPlaceholderExcludesludes(element, placeholder) {
            // exact match with the placeholder's class attribute to handle
            // the case that multiple connected sortables exist and
            // the placeholder option equals the class of sortable items
            var notCssSelector = opts['ui-model-items'].replace(/[^,]*>/g, '');
            var excludes = element.find(
              '[class="' +
                placeholder.attr('class') +
                '"]:not(' +
                notCssSelector +
                ')'
            );
            return excludes;
          }

          function hasSortingHelper(element, ui) {
            var helperOption = element.sortable('option', 'helper');
            return (
              helperOption === 'clone' ||
              (typeof helperOption === 'function' &&
                ui.item.sortable.isCustomHelperUsed())
            );
          }

          function getSortingHelper(element, ui /*, savedNodes*/) {
            var result = null;
            if (
              hasSortingHelper(element, ui) &&
              element.sortable('option', 'appendTo') === 'parent'
            ) {
              // The .ui-sortable-helper element (that's the default class name)
              result = helper;
            }
            return result;
          }

          // thanks jquery-ui
          function isFloating(item) {
            return (
              /left|right/.test(item.css('float')) ||
              /inline|table-cell/.test(item.css('display'))
            );
          }

          function getElementContext(elementScopes, element) {
            for (var i = 0; i < elementScopes.length; i++) {
              var c = elementScopes[i];
              if (c.element[0] === element[0]) {
                return c;
              }
            }
          }

          function afterStop(e, ui) {
            ui.item.sortable._destroy();
          }

          // return the index of ui.item among the items
          // we can't just do ui.item.index() because there it might have siblings
          // which are not items
          function getItemIndex(item) {
            return item
              .parent()
              .find(opts['ui-model-items'])
              .index(item);
          }

          var opts = {};

          // directive specific options
          var directiveOpts = {
            'ui-floating': undefined,
            'ui-model-items': uiSortableConfig.items,
            'ui-preserve-size': undefined
          };

          var callbacks = {
            create: null,
            start: null,
            activate: null,
            // sort: null,
            // change: null,
            // over: null,
            // out: null,
            beforeStop: null,
            update: null,
            remove: null,
            receive: null,
            deactivate: null,
            stop: null
          };

          var wrappers = {
            helper: null
          };

          angular.extend(
            opts,
            directiveOpts,
            uiSortableConfig,
            scope.uiSortable
          );

          if (!angular.element.fn || !angular.element.fn.jquery) {
            $log.error(
              'ui.sortable: jQuery should be included before AngularJS!'
            );
            return;
          }

          function wireUp() {
            // When we add or remove elements, we need the sortable to 'refresh'
            // so it can find the new/removed elements.
            scope.$watchCollection('ngModel', function() {
              // Timeout to let ng-repeat modify the DOM
              $timeout(
                function() {
                  // ensure that the jquery-ui-sortable widget instance
                  // is still bound to the directive's element
                  if (!!getSortableWidgetInstance(element)) {
                    element.sortable('refresh');
                  }
                },
                0,
                false
              );
            });

            callbacks.start = function(e, ui) {
              if (opts['ui-floating'] === 'auto') {
                // since the drag has started, the element will be
                // absolutely positioned, so we check its siblings
                var siblings = ui.item.siblings();
                var sortableWidgetInstance = getSortableWidgetInstance(
                  angular.element(e.target)
                );
                sortableWidgetInstance.floating = isFloating(siblings);
              }

              // Save the starting position of dragged item
              var index = getItemIndex(ui.item);
              ui.item.sortable = {
                model: ngModel.$modelValue[index],
                index: index,
                source: element,
                sourceList: ui.item.parent(),
                sourceModel: ngModel.$modelValue,
                cancel: function() {
                  ui.item.sortable._isCanceled = true;
                },
                isCanceled: function() {
                  return ui.item.sortable._isCanceled;
                },
                isCustomHelperUsed: function() {
                  return !!ui.item.sortable._isCustomHelperUsed;
                },
                _isCanceled: false,
                _isCustomHelperUsed: ui.item.sortable._isCustomHelperUsed,
                _destroy: function() {
                  angular.forEach(ui.item.sortable, function(value, key) {
                    ui.item.sortable[key] = undefined;
                  });
                },
                _connectedSortables: [],
                _getElementContext: function(element) {
                  return getElementContext(this._connectedSortables, element);
                }
              };
            };

            callbacks.activate = function(e, ui) {
              var isSourceContext = ui.item.sortable.source === element;
              var savedNodesOrigin = isSourceContext
                ? ui.item.sortable.sourceList
                : element;
              var elementContext = {
                element: element,
                scope: scope,
                isSourceContext: isSourceContext,
                savedNodesOrigin: savedNodesOrigin
              };
              // save the directive's scope so that it is accessible from ui.item.sortable
              ui.item.sortable._connectedSortables.push(elementContext);

              // We need to make a copy of the current element's contents so
              // we can restore it after sortable has messed it up.
              // This is inside activate (instead of start) in order to save
              // both lists when dragging between connected lists.
              savedNodes = savedNodesOrigin.contents();
              helper = ui.helper;

              // If this list has a placeholder (the connected lists won't),
              // don't inlcude it in saved nodes.
              var placeholder = getPlaceholderElement(element);
              if (placeholder && placeholder.length) {
                var excludes = getPlaceholderExcludesludes(
                  element,
                  placeholder
                );
                savedNodes = savedNodes.not(excludes);
              }
            };

            callbacks.update = function(e, ui) {
              // Save current drop position but only if this is not a second
              // update that happens when moving between lists because then
              // the value will be overwritten with the old value
              if (!ui.item.sortable.received) {
                ui.item.sortable.dropindex = getItemIndex(ui.item);
                var droptarget = ui.item
                  .parent()
                  .closest(
                    '[ui-sortable], [data-ui-sortable], [x-ui-sortable]'
                  );
                ui.item.sortable.droptarget = droptarget;
                ui.item.sortable.droptargetList = ui.item.parent();

                var droptargetContext = ui.item.sortable._getElementContext(
                  droptarget
                );
                ui.item.sortable.droptargetModel =
                  droptargetContext.scope.ngModel;

                // Cancel the sort (let ng-repeat do the sort for us)
                // Don't cancel if this is the received list because it has
                // already been canceled in the other list, and trying to cancel
                // here will mess up the DOM.
                element.sortable('cancel');
              }

              // Put the nodes back exactly the way they started (this is very
              // important because ng-repeat uses comment elements to delineate
              // the start and stop of repeat sections and sortable doesn't
              // respect their order (even if we cancel, the order of the
              // comments are still messed up).
              var sortingHelper =
                !ui.item.sortable.received &&
                getSortingHelper(element, ui, savedNodes);
              if (sortingHelper && sortingHelper.length) {
                // Restore all the savedNodes except from the sorting helper element.
                // That way it will be garbage collected.
                savedNodes = savedNodes.not(sortingHelper);
              }
              var elementContext = ui.item.sortable._getElementContext(element);
              savedNodes.appendTo(elementContext.savedNodesOrigin);

              // If this is the target connected list then
              // it's safe to clear the restored nodes since:
              // update is currently running and
              // stop is not called for the target list.
              if (ui.item.sortable.received) {
                savedNodes = null;
              }

              // If received is true (an item was dropped in from another list)
              // then we add the new item to this list otherwise wait until the
              // stop event where we will know if it was a sort or item was
              // moved here from another list
              if (ui.item.sortable.received && !ui.item.sortable.isCanceled()) {
                scope.$apply(function() {
                  ngModel.$modelValue.splice(
                    ui.item.sortable.dropindex,
                    0,
                    ui.item.sortable.moved
                  );
                });
                scope.$emit('ui-sortable:moved', ui);
              }
            };

            callbacks.stop = function(e, ui) {
              // If the received flag hasn't be set on the item, this is a
              // normal sort, if dropindex is set, the item was moved, so move
              // the items in the list.
              var wasMoved =
                'dropindex' in ui.item.sortable &&
                !ui.item.sortable.isCanceled();

              if (wasMoved && !ui.item.sortable.received) {
                scope.$apply(function() {
                  ngModel.$modelValue.splice(
                    ui.item.sortable.dropindex,
                    0,
                    ngModel.$modelValue.splice(ui.item.sortable.index, 1)[0]
                  );
                });
                scope.$emit('ui-sortable:moved', ui);
              } else if (
                !wasMoved &&
                !angular.equals(
                  element.contents().toArray(),
                  savedNodes.toArray()
                )
              ) {
                // if the item was not moved
                // and the DOM element order has changed,
                // then restore the elements
                // so that the ngRepeat's comment are correct.

                var sortingHelper = getSortingHelper(element, ui, savedNodes);
                if (sortingHelper && sortingHelper.length) {
                  // Restore all the savedNodes except from the sorting helper element.
                  // That way it will be garbage collected.
                  savedNodes = savedNodes.not(sortingHelper);
                }
                var elementContext = ui.item.sortable._getElementContext(
                  element
                );
                savedNodes.appendTo(elementContext.savedNodesOrigin);
              }

              // It's now safe to clear the savedNodes and helper
              // since stop is the last callback.
              savedNodes = null;
              helper = null;
            };

            callbacks.receive = function(e, ui) {
              // An item was dropped here from another list, set a flag on the
              // item.
              ui.item.sortable.received = true;
            };

            callbacks.remove = function(e, ui) {
              // Workaround for a problem observed in nested connected lists.
              // There should be an 'update' event before 'remove' when moving
              // elements. If the event did not fire, cancel sorting.
              if (!('dropindex' in ui.item.sortable)) {
                element.sortable('cancel');
                ui.item.sortable.cancel();
              }

              // Remove the item from this list's model and copy data into item,
              // so the next list can retrive it
              if (!ui.item.sortable.isCanceled()) {
                scope.$apply(function() {
                  ui.item.sortable.moved = ngModel.$modelValue.splice(
                    ui.item.sortable.index,
                    1
                  )[0];
                });
              }
            };

            // setup attribute handlers
            angular.forEach(callbacks, function(value, key) {
              callbacks[key] = combineCallbacks(callbacks[key], function() {
                var attrHandler = scope[key];
                var attrHandlerFn;
                if (
                  typeof attrHandler === 'function' &&
                  (
                    'uiSortable' +
                    key.substring(0, 1).toUpperCase() +
                    key.substring(1)
                  ).length &&
                  typeof (attrHandlerFn = attrHandler()) === 'function'
                ) {
                  attrHandlerFn.apply(this, arguments);
                }
              });
            });

            wrappers.helper = function(inner) {
              if (inner && typeof inner === 'function') {
                return function(e, item) {
                  var oldItemSortable = item.sortable;
                  var index = getItemIndex(item);

                  item.sortable = {
                    model: ngModel.$modelValue[index],
                    index: index,
                    source: element,
                    sourceList: item.parent(),
                    sourceModel: ngModel.$modelValue,
                    _restore: function() {
                      angular.forEach(item.sortable, function(value, key) {
                        item.sortable[key] = undefined;
                      });

                      item.sortable = oldItemSortable;
                    }
                  };

                  var innerResult = inner.apply(this, arguments);
                  item.sortable._restore();
                  item.sortable._isCustomHelperUsed = item !== innerResult;
                  return innerResult;
                };
              }
              return inner;
            };

            scope.$watchCollection(
              'uiSortable',
              function(newOpts, oldOpts) {
                // ensure that the jquery-ui-sortable widget instance
                // is still bound to the directive's element
                var sortableWidgetInstance = getSortableWidgetInstance(element);
                if (!!sortableWidgetInstance) {
                  var optsDiff = patchUISortableOptions(
                    newOpts,
                    oldOpts,
                    sortableWidgetInstance
                  );

                  if (optsDiff) {
                    element.sortable('option', optsDiff);
                  }
                }
              },
              true
            );

            patchUISortableOptions(opts);
          }

          function init() {
            if (ngModel) {
              wireUp();
            } else {
              $log.info('ui.sortable: ngModel not provided!', element);
            }

            // Create sortable
            element.sortable(opts);
          }

          function initIfEnabled() {
            if (scope.uiSortable && scope.uiSortable.disabled) {
              return false;
            }

            init();

            // Stop Watcher
            initIfEnabled.cancelWatcher();
            initIfEnabled.cancelWatcher = angular.noop;

            return true;
          }

          initIfEnabled.cancelWatcher = angular.noop;

          if (!initIfEnabled()) {
            initIfEnabled.cancelWatcher = scope.$watch(
              'uiSortable.disabled',
              initIfEnabled
            );
          }
        }
      };
    }
  ]);

})(window, window.angular);

/*!
angular-xeditable - 0.7.0
Edit-in-place for angular.js
Build date: 2017-03-24 
*/
/**
 * Angular-xeditable module 
 *
 */
angular.module('xeditable', [])


/**
 * Default options. 
 *
 * @namespace editable-options
 */
//todo: maybe better have editableDefaults, not options...
.value('editableOptions', {
  /**
   * Theme. Possible values `bs3`, `bs2`, `default`.  
   * Default is `default`
   * 
   * @var {string} theme
   * @memberOf editable-options
   */  
  theme: 'default',
  /**
   * icon_set. Possible values `font-awesome`, `default`.  
   * Default is `default`
   * 
   * @var {string} icon set
   * @memberOf editable-options
   */  
  icon_set: 'default',
  /**
   * Whether to show buttons for single editable element.  
   * Possible values `right`, `no`.  
   * Default is `right`
   * 
   * @var {string} buttons
   * @memberOf editable-options
   */    
  buttons: 'right',
  /**
   * Default value for `blur` attribute of single editable element.  
   * Can be `cancel|submit|ignore`.  
   * Default is `cancel`
   * 
   * @var {string} blurElem
   * @memberOf editable-options
   */
  blurElem: 'cancel',
  /**
   * Default value for `blur` attribute of editable form.  
   * Can be `cancel|submit|ignore`.  
   * Default is `ignore`.
   * 
   * @var {string} blurForm
   * @memberOf editable-options
   */
  blurForm: 'ignore',
  /**
   * How input elements get activated. Possible values: `focus|select|none`.  
   * Default is `focus`
   *
   * @var {string} activate
   * @memberOf editable-options
   */
  activate: 'focus',
  /**
   * Whether to disable x-editable. Can be overloaded on each element.  
   * Default is `false`
   *
   * @var {boolean} isDisabled
   * @memberOf editable-options
   */
   isDisabled: false,
  
  /**
   * Event, on which the edit mode gets activated. 
   * Can be any event.  
   * Default is `click`
   *
   * @var {string} activationEvent
   * @memberOf editable-options
   */
  activationEvent: 'click',

  /**
   * The default title of the submit button.  
   * Default is `Submit`
   *
   * @var {string} submitButtonTitle
   * @memberOf editable-options
   */
  submitButtonTitle: 'Submit',

  /**
   * The default aria label of the submit button.  
   * Default is `Submit`
   *
   * @var {string} submitButtonAriaLabel
   * @memberOf editable-options
   */
  submitButtonAriaLabel: 'Submit',

  /**
   * The default title of the cancel button.  
   * Default is `Cancel`
   *
   * @var {string} cancelButtonTitle
   * @memberOf editable-options
   */
  cancelButtonTitle: 'Cancel',

  /**
   * The default aria label of the cancel button.  
   * Default is `Cancel`
   *
   * @var {string} cancelButtonAriaLabel
   * @memberOf editable-options
   */
  cancelButtonAriaLabel: 'Cancel',

  /**
   * The default title of the clear button.  
   * Default is `Clear`
   *
   * @var {string} clearButtonTitle
   * @memberOf editable-options
   */
  clearButtonTitle: 'Clear',

  /**
   * The default aria label of the clear button.  
   * Default is `Clear`
   *
   * @var {string} clearButtonAriaLabel
   * @memberOf editable-options
   */
  clearButtonAriaLabel: 'Clear',

  /**
   * Whether to display the clear button.  
   * Default is `false`
   *
   * @var {boolean} displayClearButton
   * @memberOf editable-options
   */
  displayClearButton: false
});

/*
 Angular-ui bootstrap datepicker
 http://angular-ui.github.io/bootstrap/#/datepicker
 */
angular.module('xeditable').directive('editableBsdate', ['editableDirectiveFactory', '$injector', '$parse',
    function(editableDirectiveFactory, $injector, $parse) {

        // Constants from Angular-ui bootstrap datepicker
        uibDatepickerConfig = $injector.get('uibDatepickerConfig');
        uibDatepickerPopupConfig = $injector.get('uibDatepickerPopupConfig');

        var popupAttrNames = [
            ['eIsOpen', 'is-open'],
            ['eDateDisabled', 'date-disabled'],
            ['eDatepickerPopup', 'uib-datepicker-popup'],
            ['eShowButtonBar', 'show-button-bar'],
            ['eCurrentText', 'current-text'],
            ['eClearText', 'clear-text'],
            ['eCloseText', 'close-text'],
            ['eCloseOnDateSelection', 'close-on-date-selection'],
            ['eDatepickerAppendToBody', 'datepicker-append-to-body'],
            ['eOnOpenFocus', 'on-open-focus'],
            ['eName', 'name'],
            ['eDateDisabled', 'date-disabled'],
            ['eAltInputFormats', 'alt-input-formats']
        ];

        var dateOptionsNames = [
            ['eFormatDay', 'formatDay'],
            ['eFormatMonth', 'formatMonth'],
            ['eFormatYear', 'formatYear'],
            ['eFormatDayHeader', 'formatDayHeader'],
            ['eFormatDayTitle', 'formatDayTitle'],
            ['eFormatMonthTitle', 'formatMonthTitle'],
            ['eMaxMode', 'maxMode'],
            ['eMinMode', 'minMode'],
            ['eDatepickerMode', 'datepickerMode']
        ];

        return editableDirectiveFactory({
            directiveName: 'editableBsdate',
            inputTpl: '<div></div>',
            render: function() {
                /** This basically renders a datepicker as in the example shown in
                 **  http://angular-ui.github.io/bootstrap/#/datepicker
                 **  The attributes are all the same as in the bootstrap-ui datepicker with e- as prefix
                 **/
                this.parent.render.call(this);

                var attrs = this.attrs;
                var scope = this.scope;

                var inputDatePicker = angular.element('<input type="text" class="form-control" ng-model="$parent.$data"/>');

                inputDatePicker.attr('uib-datepicker-popup', attrs.eDatepickerPopupXEditable || uibDatepickerPopupConfig.datepickerPopup );
                inputDatePicker.attr('year-range', attrs.eYearRange || 20);
                inputDatePicker.attr('ng-readonly', attrs.eReadonly || false);

                for (var i = popupAttrNames.length - 1; i >= 0; i--) {
                    var popupAttr = attrs[popupAttrNames[i][0]];
                    if (typeof popupAttr !== 'undefined') {
                        inputDatePicker.attr(popupAttrNames[i][1], popupAttr);
                    }
                }

                if (attrs.eNgChange) {
                    inputDatePicker.attr('ng-change', attrs.eNgChange);
                    this.inputEl.removeAttr('ng-change');
                }

                if (attrs.eStyle) {
                    inputDatePicker.attr('style', attrs.eStyle);
                    this.inputEl.removeAttr('style');
                }

                var dateOptions = {
                    maxDate: scope.$eval(attrs.eMaxDate) || uibDatepickerConfig.maxDate,
                    minDate: scope.$eval(attrs.eMinDate) || uibDatepickerConfig.minDate,
                    showWeeks: attrs.eShowWeeks ? attrs.eShowWeeks.toLowerCase() === 'true' : uibDatepickerConfig.showWeeks,
                    startingDay: attrs.eStartingDay || 0,
                    initDate: scope.$eval(attrs.eInitDate) || new Date()
                };

                if (attrs.eDatepickerOptions) {
                    var eDatepickerOptions = $parse(attrs.eDatepickerOptions)(scope);
                    angular.extend(dateOptions, eDatepickerOptions);
                }

                for (var z = dateOptionsNames.length - 1; z >= 0; z--) {
                    var doAttr = attrs[dateOptionsNames[z][0]];
                    if (typeof doAttr !== 'undefined') {
                        dateOptions[dateOptionsNames[z][1]] = doAttr;
                    }
                }

                scope.dateOptions = dateOptions;

                var showCalendarButton = angular.isDefined(attrs.eShowCalendarButton) ? attrs.eShowCalendarButton : "true";

                //See if calendar button should be displayed
                if (showCalendarButton === "true") {
                    var buttonDatePicker = angular.element('<button type="button" class="btn btn-default"><i class="glyphicon glyphicon-calendar"></i></button>');
                    var buttonWrapper = angular.element('<span class="input-group-btn"></span>');

                    buttonDatePicker.attr('ng-click', attrs.eNgClick);

                    buttonWrapper.append(buttonDatePicker);

                    this.inputEl.append(buttonWrapper);
                } else {
                    //If no calendar button, display calendar popup on click of input field
                    inputDatePicker.attr('ng-click', attrs.eNgClick);
                }

                inputDatePicker.attr('datepicker-options', "dateOptions");

                this.inputEl.prepend(inputDatePicker);

                this.inputEl.removeAttr('class');
                this.inputEl.removeAttr('ng-click');
                this.inputEl.removeAttr('is-open');
                this.inputEl.removeAttr('init-date');
                this.inputEl.removeAttr('datepicker-popup');
                this.inputEl.removeAttr('required');
                this.inputEl.removeAttr('ng-model');
                this.inputEl.removeAttr('date-picker-append-to-body');
                this.inputEl.removeAttr('name');
                this.inputEl.attr('class','input-group');
            }
    });
}]);

/*
Angular-ui bootstrap editable timepicker
http://angular-ui.github.io/bootstrap/#/timepicker
*/
angular.module('xeditable').directive('editableBstime', ['editableDirectiveFactory',
  function(editableDirectiveFactory) {
    return editableDirectiveFactory({
      directiveName: 'editableBstime',
      inputTpl: '<div uib-timepicker></div>',
      render: function() {
        this.parent.render.call(this);

        // timepicker can't update model when ng-model set directly to it
        // see: https://github.com/angular-ui/bootstrap/issues/1141
        // so we wrap it into DIV
        var div = angular.element('<div class="well well-small" style="display:inline-block;"></div>');

        // move ng-model to wrapping div
        div.attr('ng-model', this.inputEl.attr('ng-model'));
        this.inputEl.removeAttr('ng-model');

        // move ng-change to wrapping div
        if(this.attrs.eNgChange) {
          div.attr('ng-change', this.inputEl.attr('ng-change'));
          this.inputEl.removeAttr('ng-change');
        }

        // wrap
        this.inputEl.wrap(div);
      }
    });
}]);
//checkbox
angular.module('xeditable').directive('editableCheckbox', ['editableDirectiveFactory',
  function(editableDirectiveFactory) {
    return editableDirectiveFactory({
      directiveName: 'editableCheckbox',
      inputTpl: '<input type="checkbox">',
      render: function() {
        this.parent.render.call(this);
        this.inputEl.wrap('<label></label>');
        
        if (this.attrs.eTitle) {
          this.inputEl.parent().append('<span>' + this.attrs.eTitle + '</span>');
       }
      },
      autosubmit: function() {
        var self = this;
        self.inputEl.bind('change', function() {
          setTimeout(function() {
            self.scope.$apply(function() {
              self.scope.$form.$submit();
            });
          }, 500);
        });
      }
    });
}]);

// checklist
angular.module('xeditable').directive('editableChecklist', [
  'editableDirectiveFactory',
  'editableNgOptionsParser',
  function(editableDirectiveFactory, editableNgOptionsParser) {
    return editableDirectiveFactory({
      directiveName: 'editableChecklist',
      inputTpl: '<span></span>',
      useCopy: true,
      render: function() {
        this.parent.render.call(this);
        var parsed = editableNgOptionsParser(this.attrs.eNgOptions);
        var ngChangeHtml = '';
        var ngChecklistComparatorHtml = '';

        if (this.attrs.eNgChange) {
          ngChangeHtml = ' ng-change="' +  this.attrs.eNgChange + '"';
        }

        if (this.attrs.eChecklistComparator) {
          ngChecklistComparatorHtml = ' checklist-comparator="' +  this.attrs.eChecklistComparator + '"';
        }
        
        var html = '<label ng-repeat="'+parsed.ngRepeat+'">'+
          '<input type="checkbox" checklist-model="$parent.$parent.$data" checklist-value="'+parsed.locals.valueFn+'"' +
            ngChangeHtml + ngChecklistComparatorHtml + '>'+
          '<span ng-bind="'+parsed.locals.displayFn+'"></span></label>';

        this.inputEl.removeAttr('ng-model');
        this.inputEl.removeAttr('ng-options');
        this.inputEl.removeAttr('ng-change');
        this.inputEl.removeAttr('checklist-comparator');
        this.inputEl.html(html);
      }
    });
}]);

angular.module('xeditable').directive('editableCombodate', ['editableDirectiveFactory', 'editableCombodate',
  function(editableDirectiveFactory, editableCombodate) {
    return editableDirectiveFactory({
      directiveName: 'editableCombodate',
      inputTpl: '<input type="text">',
      render: function() {
        this.parent.render.call(this);

        var options = {
          value: new Date(this.scope.$data)
        };
        var self = this;
        angular.forEach(["format", "template", "minYear", "maxYear", "yearDescending", "minuteStep", "secondStep", "firstItem", "errorClass", "customClass", "roundTime", "smartDays"], function(name) {

          var attrName = "e" + name.charAt(0).toUpperCase() + name.slice(1);

          if (attrName in self.attrs) {
            if (name == "minYear" || name == "maxYear" || name == "minuteStep" || name == "secondStep") {
              options[name] = parseInt(self.attrs[attrName], 10);
            } else {
              options[name] = self.attrs[attrName];
            }
          }
        });

        var combodate = editableCombodate.getInstance(this.inputEl, options);
        combodate.$widget.find('select').bind('change', function(e) {
          //.replace is so this works in Safari
          self.scope.$data = combodate.getValue() ?
              (new Date(combodate.getValue().replace(/-/g, "/"))).toISOString() : null;
        });
      }
    });
  }
]);

/*
Input types: text|password|email|tel|number|url|search|color|date|datetime|datetime-local|time|month|week|file
*/

(function() {

  var camelCase = function(dashDelimitedString) {
    return dashDelimitedString.toLowerCase().replace(/-(.)/g, function(match, word) {
      return word.toUpperCase();
    });
  };

  var types = 'text|password|email|tel|number|url|search|color|date|datetime|datetime-local|time|month|week|file'.split('|');

  //todo: datalist
  
  // generate directives
  angular.forEach(types, function(type) {
    var directiveName = camelCase('editable' + '-' + type);
    angular.module('xeditable').directive(directiveName, ['editableDirectiveFactory',
      function(editableDirectiveFactory) {
        return editableDirectiveFactory({
          directiveName: directiveName,
          inputTpl: '<input type="'+type+'">',
          render: function() {
            this.parent.render.call(this);

            //Add bootstrap simple input groups
            if (this.attrs.eInputgroupleft || this.attrs.eInputgroupright) {
              this.inputEl.wrap('<div class="input-group"></div>');

              if (this.attrs.eInputgroupleft) {
                var inputGroupLeft = angular.element('<span class="input-group-addon">' + this.attrs.eInputgroupleft + '</span>');
                this.inputEl.parent().prepend(inputGroupLeft);
              }

              if (this.attrs.eInputgroupright) {
                var inputGroupRight = angular.element('<span class="input-group-addon">' + this.attrs.eInputgroupright + '</span>');
                this.inputEl.parent().append(inputGroupRight);
              }
            }

            // Add label to the input
            if (this.attrs.eLabel) {
              var label = angular.element('<label>' + this.attrs.eLabel + '</label>');
              if (this.attrs.eInputgroupleft || this.attrs.eInputgroupright) {
                this.inputEl.parent().parent().prepend(label);
              } else {
                this.inputEl.parent().prepend(label);
              }
            }
            
            // Add classes to the form
            if (this.attrs.eFormclass) {
              this.editorEl.addClass(this.attrs.eFormclass);
            }
          },
          autosubmit: function() {
            var self = this;
            self.inputEl.bind('keydown', function(e) {
                //submit on tab
                if (e.keyCode === 9 && self.editorEl.attr('blur') === 'submit') {
                    self.scope.$apply(function() {
                        self.scope.$form.$submit();
                    });
                }
            });
          }
        });
    }]);
  });

  //`range` is bit specific
  angular.module('xeditable').directive('editableRange', ['editableDirectiveFactory', '$interpolate',
    function(editableDirectiveFactory, $interpolate) {
      return editableDirectiveFactory({
        directiveName: 'editableRange',
        inputTpl: '<input type="range" id="range" name="range">',
        render: function() {
          this.parent.render.call(this);
          this.inputEl.after('<output>' + $interpolate.startSymbol() + '$data' + $interpolate.endSymbol()  + '</output>');
        }        
      });
  }]);

}());


/*
 Tags input directive for AngularJS
 https://github.com/mbenford/ngTagsInput
 */
angular.module('xeditable').directive('editableTagsInput', ['editableDirectiveFactory', 'editableUtils',
  function(editableDirectiveFactory, editableUtils) {
    var dir = editableDirectiveFactory({
        directiveName: 'editableTagsInput',
        inputTpl: '<tags-input></tags-input>',
        useCopy: true,
        render: function () {
            this.parent.render.call(this);
            this.inputEl.append(editableUtils.rename('auto-complete', this.attrs.$autoCompleteElement));
            this.inputEl.removeAttr('ng-model');
            this.inputEl.attr('ng-model', '$parent.$data');
        }
    });

    var linkOrg = dir.link;

    dir.link = function (scope, el, attrs, ctrl) {
        var autoCompleteEl = el.find('editable-tags-input-auto-complete');

        attrs.$autoCompleteElement = autoCompleteEl.clone();

        autoCompleteEl.remove();

        return linkOrg(scope, el, attrs, ctrl);
    };

    return dir;
}]);
// radiolist
angular.module('xeditable').directive('editableRadiolist', [
  'editableDirectiveFactory',
  'editableNgOptionsParser',
  '$interpolate',
  function(editableDirectiveFactory, editableNgOptionsParser, $interpolate) {
    return editableDirectiveFactory({
      directiveName: 'editableRadiolist',
      inputTpl: '<span></span>',
      render: function() {
        this.parent.render.call(this);
        var parsed = editableNgOptionsParser(this.attrs.eNgOptions),
            ngChangeHtml = '',
            ngNameHtml = '';

        if (this.attrs.eNgChange) {
          ngChangeHtml = ' ng-change="' +  this.attrs.eNgChange + '"';
        }
        
        if (this.attrs.eName) {
            ngNameHtml = ' name="' +  this.attrs.eName + '"';
        }
          
        var html = '<label data-ng-repeat="'+parsed.ngRepeat+'">'+
          '<input type="radio" data-ng-disabled="::' +
            this.attrs.eNgDisabled +
            '" data-ng-model="$parent.$parent.$data" data-ng-value="' + $interpolate.startSymbol() +
            '::' + parsed.locals.valueFn + $interpolate.endSymbol() +'"' +
            ngChangeHtml + ngNameHtml + '>'+
          '<span data-ng-bind="::'+parsed.locals.displayFn+'"></span></label>';

        this.inputEl.removeAttr('ng-model');
        this.inputEl.removeAttr('ng-options');
        this.inputEl.removeAttr('ng-change');
        this.inputEl.html(html);
      },
      autosubmit: function() {
        var self = this;
        self.inputEl.bind('change', function() {
          setTimeout(function() {
            self.scope.$apply(function() {
              self.scope.$form.$submit();
            });
          }, 500);
        });
      }
    });
}]);

//select
angular.module('xeditable').directive('editableSelect', ['editableDirectiveFactory',
  function(editableDirectiveFactory) {
    return editableDirectiveFactory({
      directiveName: 'editableSelect',
      inputTpl: '<select></select>',
      render: function() {
        this.parent.render.call(this);

        if (this.attrs.ePlaceholder) {
          var placeholder = angular.element('<option value="">' + this.attrs.ePlaceholder + '</option>');
          this.inputEl.append(placeholder);
        }
      },
      autosubmit: function() {
        var self = this;

        if (!self.attrs.hasOwnProperty("eMultiple")) {
          self.inputEl.bind('change', function () {
            self.scope.$apply(function () {
                self.scope.$form.$submit();
            });
          });
        }
      }
    });
}]);
//textarea
angular.module('xeditable').directive('editableTextarea', ['editableDirectiveFactory',
  function(editableDirectiveFactory) {
    return editableDirectiveFactory({
      directiveName: 'editableTextarea',
      inputTpl: '<textarea></textarea>',
      addListeners: function() {
        var self = this;
        self.parent.addListeners.call(self);
        // submit textarea by ctrl+enter even with buttons
        if (self.single && self.buttons !== 'no') {
          self.autosubmit();
        }
      },
      autosubmit: function() {
        var self = this;
        self.inputEl.bind('keydown', function(e) {
          if (self.attrs.submitOnEnter) {
            if (e.keyCode === 13 && !e.shiftKey) {
              self.scope.$apply(function() {
                self.scope.$form.$submit();
              });
            }
          } else if ((e.ctrlKey || e.metaKey) && (e.keyCode === 13) || 
                (e.keyCode === 9 && self.editorEl.attr('blur') === 'submit')) {
            self.scope.$apply(function() {
              self.scope.$form.$submit();
            });
          }
        });
      }
    });
}]);

/*
 jQuery UI Datepicker for AngularJS
 https://github.com/angular-ui/ui-date
 */
angular.module('xeditable').directive('editableUidate', ['editableDirectiveFactory',
    function(editableDirectiveFactory) {
        return editableDirectiveFactory({
            directiveName: 'editableUidate',
            inputTpl: '<input class="form-control" />',
            render: function() {
                this.parent.render.call(this);
                this.inputEl.attr('ui-date', this.attrs.eUiDate);
                this.inputEl.attr('placeholder', this.attrs.ePlaceholder);
            }
        });
}]);

/*
 AngularJS-native version of Select2 and Selectize
 https://github.com/angular-ui/ui-select
 */
angular.module('xeditable').directive('editableUiSelect',['editableDirectiveFactory', 'editableUtils',
    function(editableDirectiveFactory, editableUtils) {
        var dir = editableDirectiveFactory({
            directiveName: 'editableUiSelect',
            inputTpl: '<ui-select></ui-select>',
            render: function () {
                this.parent.render.call(this);
                this.inputEl.append(editableUtils.rename('ui-select-match', this.attrs.$matchElement));
                this.inputEl.append(editableUtils.rename('ui-select-choices', this.attrs.$choicesElement));
                this.inputEl.removeAttr('ng-model');
                this.inputEl.attr('ng-model', '$parent.$parent.$data');
            }
        });

        var linkOrg = dir.link;

        dir.link = function (scope, el, attrs, ctrl) {
            var matchEl = el.find('editable-ui-select-match');
            var choicesEl = el.find('editable-ui-select-choices');

            attrs.$matchElement = matchEl.clone();
            attrs.$choicesElement = choicesEl.clone();

            matchEl.remove();
            choicesEl.remove();

            return linkOrg(scope, el, attrs, ctrl);
        };

        return dir;
    }]);
/**
 * EditableController class. 
 * Attached to element with `editable-xxx` directive.
 *
 * @namespace editable-element
 */
/*
TODO: this file should be refactored to work more clear without closures!
*/
angular.module('xeditable').factory('editableController', 
  ['$q', 'editableUtils',
  function($q, editableUtils) {

  //EditableController function
  EditableController.$inject = ['$scope', '$attrs', '$element', '$parse', 'editableThemes', 'editableIcons', 'editableOptions', '$rootScope', '$compile', '$q', '$sce'];
  function EditableController($scope, $attrs, $element, $parse, editableThemes, editableIcons, editableOptions, $rootScope, $compile, $q, $sce) {
    var valueGetter;

    //if control is disabled - it does not participate in waiting process
    var inWaiting;

    var self = this;

    self.scope = $scope;
    self.elem = $element;
    self.attrs = $attrs;
    self.inputEl = null;
    self.editorEl = null;
    self.single = true;
    self.error = '';
    self.theme =  editableThemes[$attrs.editableTheme] || editableThemes[editableOptions.theme] || editableThemes['default'];
    self.parent = {};

    //will be undefined if icon_set is default and theme is default
    var theme_name = $attrs.editableTheme || editableOptions.theme || 'default';
    // The theme_name will not be correct if the theme set in options is unavailable
    // However, in that case an undefined icon_set is not that bad...
    var icon_set_option = $attrs.editableIconSet || editableOptions.icon_set;
    self.icon_set = icon_set_option === 'default' ? editableIcons.default[theme_name] : editableIcons.external[icon_set_option];

    //to be overwritten by directive
    self.inputTpl = '';
    self.directiveName = '';

    // with majority of controls copy is not needed, but..
    // copy MUST NOT be used for `select-multiple` with objects as items
    // copy MUST be used for `checklist`
    self.useCopy = false;

    //runtime (defaults)
    self.single = null;

    /**
     * Attributes defined with `e-*` prefix automatically transferred from original element to
     * control.  
     * For example, if you set `<span editable-text="user.name" e-style="width: 100px"`>
     * then input will appear as `<input style="width: 100px">`.  
     * See [demo](#text-customize).
     * 
     * @var {any|attribute} e-*
     * @memberOf editable-element
     */ 

    /**
     * Whether to show ok/cancel buttons. Values: `right|no`.
     * If set to `no` control automatically submitted when value changed.  
     * If control is part of form buttons will never be shown. 
     * 
     * @var {string|attribute} buttons
     * @memberOf editable-element
     */    
    self.buttons = 'right'; 
    /**
     * Action when control losses focus. Values: `cancel|submit|ignore`.
     * Has sense only for single editable element.
     * Otherwise, if control is part of form - you should set `blur` of form, not of individual element.
     * 
     * @var {string|attribute} blur
     * @memberOf editable-element
     */     
    // no real `blur` property as it is transferred to editable form

    //init
    self.init = function(single) {
      self.single = single;

      self.name = $attrs.eName || $attrs[self.directiveName];
      /*
      if(!$attrs[directiveName] && !$attrs.eNgModel && ($attrs.eValue === undefined)) {
        throw 'You should provide value for `'+directiveName+'` or `e-value` in editable element!';
      }
      */
      if($attrs[self.directiveName]) {
        valueGetter = $parse($attrs[self.directiveName]);
      } else {
        throw 'You should provide value for `'+self.directiveName+'` in editable element!';
      }

      // settings for single and non-single
      if (!self.single) {
        // hide buttons for non-single
        self.buttons = 'no';
      } else {
        self.buttons = self.attrs.buttons || editableOptions.buttons;
      }

      //if name defined --> watch changes and update $data in form
      if($attrs.eName) {
        self.scope.$watch('$data', function(newVal){
          self.scope.$form.$data[$attrs.eName] = newVal;
        });
      }

      /**
       * Called when control is shown.  
       * See [demo](#select-remote).
       * 
       * @var {method|attribute} onshow
       * @memberOf editable-element
       */
      if($attrs.onshow) {
        self.onshow = function() {
          return self.catchError($parse($attrs.onshow)($scope));
        };
      }

      /**
       * Called when control is hidden after both save or cancel.  
       * 
       * @var {method|attribute} onhide
       * @memberOf editable-element
       */
      if($attrs.onhide) {
        self.onhide = function() {
          return $parse($attrs.onhide)($scope);
        };
      }

      /**
       * Called when control is cancelled.  
       * 
       * @var {method|attribute} oncancel
       * @memberOf editable-element
       */
      if($attrs.oncancel) {
        self.oncancel = function() {
          return $parse($attrs.oncancel)($scope);
        };
      }          

      /**
       * Called during submit before value is saved to model.  
       * See [demo](#onbeforesave).
       * 
       * @var {method|attribute} onbeforesave
       * @memberOf editable-element
       */
      if ($attrs.onbeforesave) {
        self.onbeforesave = function() {
          return self.catchError($parse($attrs.onbeforesave)($scope));
        };
      }

      /**
       * Called during submit after value is saved to model.  
       * See [demo](#onaftersave).
       * 
       * @var {method|attribute} onaftersave
       * @memberOf editable-element
       */
      if ($attrs.onaftersave) {
        self.onaftersave = function() {
          return self.catchError($parse($attrs.onaftersave)($scope));
        };
      }

      // watch change of model to update editable element
      // now only add/remove `editable-empty` class.
      // Initially this method called with newVal = undefined, oldVal = undefined
      // so no need initially call handleEmpty() explicitly
      $scope.$parent.$watch($attrs[self.directiveName], function(newVal, oldVal) {
        self.setLocalValue();
        self.handleEmpty();
      });
    };

    self.render = function() {
      var theme = self.theme;

      //build input
      self.inputEl = angular.element(self.inputTpl);

      //build controls
      self.controlsEl = angular.element(theme.controlsTpl);
      self.controlsEl.append(self.inputEl);

      //build buttons
      if(self.buttons !== 'no') {
        self.buttonsEl = angular.element(theme.buttonsTpl);
        self.submitEl = angular.element(theme.submitTpl);
        self.resetEl = angular.element(theme.resetTpl);
        self.cancelEl = angular.element(theme.cancelTpl);
        self.submitEl.attr('title', editableOptions.submitButtonTitle);
        self.submitEl.attr('aria-label', editableOptions.submitButtonAriaLabel);
        self.cancelEl.attr('title', editableOptions.cancelButtonTitle);
        self.cancelEl.attr('aria-label', editableOptions.cancelButtonAriaLabel);
        self.resetEl.attr('title', editableOptions.clearButtonTitle);
        self.resetEl.attr('aria-label', editableOptions.clearButtonAriaLabel);

        if (self.icon_set) {
          self.submitEl.find('span').addClass(self.icon_set.ok);
          self.cancelEl.find('span').addClass(self.icon_set.cancel);
          self.resetEl.find('span').addClass(self.icon_set.clear);
        }

        self.buttonsEl.append(self.submitEl).append(self.cancelEl);

        if (editableOptions.displayClearButton) {
          self.buttonsEl.append(self.resetEl);
        }

        self.controlsEl.append(self.buttonsEl);
        
        self.inputEl.addClass('editable-has-buttons');
      }

      //build error
      self.errorEl = angular.element(theme.errorTpl);
      self.controlsEl.append(self.errorEl);

      //build editor
      self.editorEl = angular.element(self.single ? theme.formTpl : theme.noformTpl);
      self.editorEl.append(self.controlsEl);

      // transfer `e-*|data-e-*|x-e-*` attributes
      for (var k in $attrs.$attr) {
        if(k.length <= 1) {
          continue;
        }
        var transferAttr = false;
        var nextLetter = k.substring(1, 2);

        // if starts with `e` + uppercase letter
        if (k.substring(0, 1) === 'e' && nextLetter === nextLetter.toUpperCase()) {
          transferAttr = k.substring(1); // cut `e`
        } else {
          continue;
        }
        
        // exclude `form` and `ng-submit`, 
        if (transferAttr === 'Form' || transferAttr === 'NgSubmit') {
          continue;
        }

        var firstLetter = transferAttr.substring(0, 1);
        var secondLetter = transferAttr.substring(1, 2);

        // convert back to lowercase style
        if (secondLetter === secondLetter.toUpperCase() &&
            firstLetter === firstLetter.toUpperCase()) {
          transferAttr = firstLetter.toLowerCase() + '-' + editableUtils.camelToDash(transferAttr.substring(1));
        } else {
          transferAttr = firstLetter.toLowerCase() + editableUtils.camelToDash(transferAttr.substring(1));
        }

        // workaround for attributes without value (e.g. `multiple = "multiple"`)
        // except for 'e-value'
        var attrValue = (transferAttr !== 'value' && $attrs[k] === '') ? transferAttr : $attrs[k];

        // set attributes to input
        self.inputEl.attr(transferAttr, attrValue);
      }

      self.inputEl.addClass('editable-input');
      self.inputEl.attr('ng-model', '$parent.$data');

      // add directiveName class to editor, e.g. `editable-text`
      self.editorEl.addClass(editableUtils.camelToDash(self.directiveName));

      if (self.single) {
        self.editorEl.attr('editable-form', '$form');
        // transfer `blur` to form
        self.editorEl.attr('blur', self.attrs.blur || (self.buttons === 'no' ? 'cancel' : editableOptions.blurElem));
      }

      //apply `postrender` method of theme
      if (angular.isFunction(theme.postrender)) {
        theme.postrender.call(self);
      }

    };

    // with majority of controls copy is not needed, but..
    // copy MUST NOT be used for `select-multiple` with objects as items
    // copy MUST be used for `checklist`
    self.setLocalValue = function() {
      self.scope.$data = self.useCopy ? 
        angular.copy(valueGetter($scope.$parent)) : 
        valueGetter($scope.$parent);
    };

    // reference of the scope to use for $compile
    var newScope = null;
    //show
    self.show = function() {
      // set value of scope.$data
      self.setLocalValue();

      /*
      Originally render() was inside init() method, but some directives polluting editorEl,
      so it is broken on second openning.
      Cloning is not a solution as jqLite can not clone with event handler's.
      */
      self.render();

      // insert into DOM
      $element.after(self.editorEl);

      // compile (needed to attach ng-* events from markup)
      newScope = $scope.$new();
      $compile(self.editorEl)(newScope);

      // attach listeners (`escape`, autosubmit, etc)
      self.addListeners();

      // hide element
      $element.addClass('editable-hide');

      // onshow
      return self.onshow();
    };

    //hide
    self.hide = function() {

      // destroy the scope to prevent memory leak
      newScope.$destroy();

      self.controlsEl.remove();
      self.editorEl.remove();
      $element.removeClass('editable-hide');

      // onhide
      return self.onhide();
    };

    // cancel
    self.cancel = function() {
      // oncancel
      self.oncancel();
      // don't call hide() here as it called in form's code
    };

    /*
    Called after show to attach listeners
    */
    self.addListeners = function() {
      // bind keyup for `escape`
      self.inputEl.bind('keyup', function(e) {
          if(!self.single) {
            return;
          }

          // todo: move this to editable-form!
          switch(e.keyCode) {
            // hide on `escape` press
            case 27:
              self.scope.$apply(function() {
                self.scope.$form.$cancel();
              });
            break;
          }
      });

      // autosubmit when `no buttons`
      if (self.single && self.buttons === 'no') {
        self.autosubmit();
      }

      // click - mark element as clicked to exclude in document click handler
      self.editorEl.bind('click', function(e) {
        // ignore right/middle button click
        if (e.which && e.which !== 1) {
          return;
        }

        if (self.scope.$form.$visible) {
          self.scope.$form._clicked = true;
        }
      });
    };

    // setWaiting
    self.setWaiting = function(value) {
      if (value) {
        // participate in waiting only if not disabled
        inWaiting = !self.inputEl.attr('disabled') &&
                    !self.inputEl.attr('ng-disabled') &&
                    !self.inputEl.attr('ng-enabled');
        if (inWaiting) {
          self.inputEl.attr('disabled', 'disabled');
          if(self.buttonsEl) {
            self.buttonsEl.find('button').attr('disabled', 'disabled');
          }
        }
      } else {
        if (inWaiting) {
          self.inputEl.removeAttr('disabled');
          if (self.buttonsEl) {
            self.buttonsEl.find('button').removeAttr('disabled');
          }
        }
      }
    };

    self.activate = function(start, end) {
      setTimeout(function() {
        var el = self.inputEl[0];

        if (editableOptions.activate === 'focus' && el.focus) {
          if (start !== undefined && start !== "" && el.setSelectionRange) {
            end = end || start;
            el.onfocus = function() {
              setTimeout(function() {
                try {
                  this.setSelectionRange(start,end);
                } catch(e) {
                  //do nothing, this input doesn't support setSelectionRange
                }
              }.bind(this));
            };
          }
          
          if (self.directiveName == 'editableRadiolist' || self.directiveName == 'editableChecklist' ||
              self.directiveName == 'editableBsdate' || self.directiveName == 'editableTagsInput') {
            //Set focus to first pristine element in the list
            el.querySelector('.ng-pristine').focus();
          } else {
            el.focus();
          }
        } else if (editableOptions.activate === 'select') {
          if (el.select){
            el.select();
          } else if (el.focus) {
            el.focus();
          }
        }
      }, 0);
    };

    self.setError = function(msg) {
      if(!angular.isObject(msg)) {
        $scope.$error = $sce.trustAsHtml(msg);
        self.error = msg;
      }
    };

    /*
    Checks that result is string or promise returned string and shows it as error message
    Applied to onshow, onbeforesave, onaftersave
    */
    self.catchError = function(result, noPromise) {
      if (angular.isObject(result) && noPromise !== true) {
        $q.when(result).then(
          //success and fail handlers are equal
          angular.bind(this, function(r) {
            this.catchError(r, true);
          }),
          angular.bind(this, function(r) {
            this.catchError(r, true);
          })
        );
      //check $http error
      } else if (noPromise && angular.isObject(result) && result.status &&
        (result.status !== 200) && result.data && angular.isString(result.data)) {
        this.setError(result.data);
        //set result to string: to let form know that there was error
        result = result.data;
      } else if (angular.isString(result)) {
        this.setError(result);
      }
      return result;
    };

    self.save = function() {
      valueGetter.assign($scope.$parent,
          self.useCopy ? angular.copy(self.scope.$data) : self.scope.$data);

      // no need to call handleEmpty here as we are watching change of model value
      // self.handleEmpty();
    };

    /*
    attach/detach `editable-empty` class to element
    */
    self.handleEmpty = function() {
      var val = valueGetter($scope.$parent);
      var isEmpty = val === null || val === undefined || val === "" || (angular.isArray(val) && val.length === 0); 
      $element.toggleClass('editable-empty', isEmpty);
    };

    /*
    Called when `buttons = "no"` to submit automatically
    */
    self.autosubmit = angular.noop;

    self.onshow = angular.noop;
    self.onhide = angular.noop;
    self.oncancel = angular.noop;
    self.onbeforesave = angular.noop;
    self.onaftersave = angular.noop;
  }

  return EditableController;
}]);

/*
editableFactory is used to generate editable directives (see `/directives` folder)
Inside it does several things:
- detect form for editable element. Form may be one of three types:
  1. autogenerated form (for single editable elements)
  2. wrapper form (element wrapped by <form> tag)
  3. linked form (element has `e-form` attribute pointing to existing form)

- attach editableController to element

Depends on: editableController, editableFormFactory
*/
angular.module('xeditable').factory('editableDirectiveFactory',
['$parse', '$compile', 'editableThemes', '$rootScope', '$document', 'editableController', 'editableFormController', 'editableOptions',
function($parse, $compile, editableThemes, $rootScope, $document, editableController, editableFormController, editableOptions) {

  //directive object
  return function(overwrites) {
    return {
      restrict: 'A',
      scope: true,
      require: [overwrites.directiveName, '?^form'],
      controller: editableController,
      link: function(scope, elem, attrs, ctrl) {
        // editable controller
        var eCtrl = ctrl[0];

        // form controller
        var eFormCtrl;

        // this variable indicates is element is bound to some existing form,
        // or it's single element who's form will be generated automatically
        // By default consider single element without any linked form.ß
        var hasForm = false;

        // element wrapped by form
        if (ctrl[1]) {
          eFormCtrl = ctrl[1];
          hasForm = attrs.eSingle === undefined;
        } else if (attrs.eForm) { // element not wrapped by <form>, but we have `e-form` attr
          var getter = $parse(attrs.eForm)(scope);
          if (getter) { // form exists in scope (above), e.g. editable column
            eFormCtrl = getter;
            hasForm = true;
          } else if (elem && typeof elem.parents === "function" && elem.parents().last().find('form[name='+attrs.eForm+']').length) { // form exists below or not exist at all: check document.forms
            // form is below and not processed yet
            eFormCtrl = null;
            hasForm = true;
          } else {
            // form exists below or not exist at all: check document.forms
            for (var i=0; i<$document[0].forms.length;i++) {
              if ($document[0].forms[i].name === attrs.eForm) {
                // form is below and not processed yet
                eFormCtrl = null;
                hasForm = true;
                break;
              }
            }
          }
        }

        /*
        if(hasForm && !attrs.eName) {
          throw 'You should provide `e-name` for editable element inside form!';
        }
        */

        //check for `editable-form` attr in form
        /*
        if(eFormCtrl && ) {
          throw 'You should provide `e-name` for editable element inside form!';
        }
        */

        // store original props to `parent` before merge
        angular.forEach(overwrites, function(v, k) {
          if(eCtrl[k] !== undefined) {
            eCtrl.parent[k] = eCtrl[k];
          }
        });

        // merge overwrites to base editable controller
        angular.extend(eCtrl, overwrites);

        // x-editable can be disabled using editableOption or edit-disabled attribute
        var is_disabled = function() {
          return angular.isDefined(attrs.editDisabled) ?
            scope.$eval(attrs.editDisabled) :
            editableOptions.isDisabled;
        };

        // init editable ctrl
        eCtrl.init(!hasForm);

        // publich editable controller as `$editable` to be referenced in html
        scope.$editable = eCtrl;

        // add `editable` class to element
        elem.addClass('editable');

        // hasForm
        if(hasForm) {
          if(eFormCtrl) {
            scope.$form = eFormCtrl;
            if(!scope.$form.$addEditable) {
              throw 'Form with editable elements should have `editable-form` attribute.';
            }
            scope.$form.$addEditable(eCtrl);
          } else {
            // future form (below): add editable controller to buffer and add to form later
            $rootScope.$$editableBuffer = $rootScope.$$editableBuffer || {};
            $rootScope.$$editableBuffer[attrs.eForm] = $rootScope.$$editableBuffer[attrs.eForm] || [];
            $rootScope.$$editableBuffer[attrs.eForm].push(eCtrl);
            scope.$form = null; //will be re-assigned later
          }
        // !hasForm
        } else {
          // create editableform controller
          scope.$form = editableFormController();
          // add self to editable controller
          scope.$form.$addEditable(eCtrl);

          // if `e-form` provided, publish local $form in scope
          if(attrs.eForm) {
            ($parse(attrs.eForm).assign || angular.noop)(scope.$parent, scope.$form);
          }

          // bind click - if no external form defined
          if(!attrs.eForm || attrs.eClickable) {
            elem.addClass('editable-click');
            elem.bind(editableOptions.activationEvent, function(e) {
              e.preventDefault();
              e.editable = eCtrl;
              if(!is_disabled()) {
                scope.$apply(function(){
                  scope.$form.$show();
                });
              }
            });
          }
        }

      }
    };
  };
}]);

/*
Returns editableForm controller
*/
angular.module('xeditable').factory('editableFormController', 
  ['$parse', '$document', '$rootScope', 'editablePromiseCollection', 'editableUtils',
  function($parse, $document, $rootScope, editablePromiseCollection, editableUtils) {

  // array of opened editable forms
  var shown = [];

  //Check if the child element correspond or is a descendant of the parent element
  var isSelfOrDescendant = function (parent, child) {
    if (child == parent) {
      return true;
    }

    var node = child.parentNode;
    while (node !== null) {
      if (node == parent) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  };
  
  //Check if it is a real blur : if the click event appear on a shown editable elem, this is not a blur.
  var isBlur = function(shown, event) {
    var isBlur = true;

    var editables = shown.$editables;
    angular.forEach(editables, function(v){
      var element = v.editorEl[0];
      if (isSelfOrDescendant(element, event.target))
        isBlur = false;
      
    });
    return isBlur;
  };
  
  // bind click to body: cancel|submit|ignore forms
  $document.bind('click', function(e) {
    // ignore right/middle button click
    if (e.which && e.which !== 1) {
      return;
    }

    var toCancel = [];
    var toSubmit = [];
    for (var i=0; i<shown.length; i++) {

      // exclude clicked
      if (shown[i]._clicked) {
        shown[i]._clicked = false;
        continue;
      }

      // exclude waiting
      if (shown[i].$waiting) {
        continue;
      }

      if (shown[i]._blur === 'cancel' && isBlur(shown[i], e)) {
        toCancel.push(shown[i]);
      }

      if (shown[i]._blur === 'submit' && isBlur(shown[i], e)) {
        toSubmit.push(shown[i]);
      }
    }

    if (toCancel.length || toSubmit.length) {
      $rootScope.$apply(function() {
        angular.forEach(toCancel, function(v){ v.$cancel(); });
        angular.forEach(toSubmit, function(v){ v.$submit(); });
      });
    }
  });
 
  $rootScope.$on('closeEdit', function() {
    for(var i=0; i < shown.length; i++) {
      shown[i].$hide();
    }
  }); 

  var base = {
    $addEditable: function(editable) {
      //console.log('add editable', editable.elem, editable.elem.bind);
      this.$editables.push(editable);

      //'on' is not supported in angular 1.0.8
      editable.elem.bind('$destroy', angular.bind(this, this.$removeEditable, editable));

      //bind editable's local $form to self (if not bound yet, below form) 
      if (!editable.scope.$form) {
        editable.scope.$form = this;
      }

      //if form already shown - call show() of new editable
      if (this.$visible) {
        editable.catchError(editable.show());
      }
      editable.catchError(editable.setWaiting(this.$waiting));
    },

    $removeEditable: function(editable) {
      //arrayRemove
      for(var i=0; i < this.$editables.length; i++) {
        if(this.$editables[i] === editable) {
          this.$editables.splice(i, 1);
          return;
        }
      }
    },

    /**
     * Shows form with editable controls.
     * 
     * @method $show()
     * @memberOf editable-form
     */
    $show: function() {
      if (this.$visible) {
        return;
      }

      this.$visible = true;

      var pc = editablePromiseCollection();

      //own show
      pc.when(this.$onshow());

      //clear errors
      this.$setError(null, '');

      //children show
      angular.forEach(this.$editables, function(editable) {
        pc.when(editable.show());
      });

      //wait promises and activate
      pc.then({
        onWait: angular.bind(this, this.$setWaiting), 
        onTrue: angular.bind(this, this.$activate), 
        onFalse: angular.bind(this, this.$activate), 
        onString: angular.bind(this, this.$activate)
      });

      // add to internal list of shown forms
      // setTimeout needed to prevent closing right after opening (e.g. when trigger by button)
      setTimeout(angular.bind(this, function() {
        // clear `clicked` to get ready for clicks on visible form
        this._clicked = false;
        if(editableUtils.indexOf(shown, this) === -1) {
          shown.push(this);
        }
      }), 0);      
    },

    /**
     * Sets focus on form field specified by `name`.<br/>
     * When trying to set the focus on a form field of a new row in the editable table, the `$activate` call needs to be wrapped in a `$timeout` call so that the form is rendered before the `$activate` function is called.
     * 
     * @method $activate(name)
     * @param {string} name name of field
     * @memberOf editable-form
     */
    $activate: function(name) {
      var i,
          selectionStart,
          selectionEnd;
      if (this.$editables.length) {
        //activate by name
        if (angular.isString(name)) {
          for(i=0; i<this.$editables.length; i++) {
            if (this.$editables[i].name === name) {
              this.$editables[i].activate();
              return;
            }
          }
        }

        //try activate error field
        for(i=0; i<this.$editables.length; i++) {
          if (this.$editables[i].error) {
            this.$editables[i].activate();
            return;
          }
        }

        //by default activate first field
        selectionStart = this.$editables[0].elem[0].selectionStart ? 
            this.$editables[0].elem[0].selectionStart : 
              this.$editables[0].elem[0].text ? this.$editables[0].elem[0].text.length :
                  this.$editables[0].elem[0].innerHTML ? this.$editables[0].elem[0].innerHTML.length : 0;
        selectionEnd = this.$editables[0].elem[0].selectionEnd ? 
            this.$editables[0].elem[0].selectionEnd : 
              this.$editables[0].elem[0].text ? this.$editables[0].elem[0].text.length :
                  this.$editables[0].elem[0].innerHTML ? this.$editables[0].elem[0].innerHTML.length : 0;
        this.$editables[0].activate(selectionStart, selectionEnd);
      }
    },

    /**
     * Hides form with editable controls without saving.
     * 
     * @method $hide()
     * @memberOf editable-form
     */
    $hide: function() {
      if (!this.$visible) {
        return;
      }      
      this.$visible = false;
      // self hide
      this.$onhide();
      // children's hide
      angular.forEach(this.$editables, function(editable) {
        editable.hide();
      });

      // remove from internal list of shown forms
      editableUtils.arrayRemove(shown, this);
    },

    /**
     * Triggers `oncancel` event and calls `$hide()`.
     * 
     * @method $cancel()
     * @memberOf editable-form
     */
    $cancel: function() {
      if (!this.$visible) {
        return;
      }      
      // self cancel
      this.$oncancel();
      // children's cancel      
      angular.forEach(this.$editables, function(editable) {
        editable.cancel();
      });
      // self hide
      this.$hide();
    },    

    $setWaiting: function(value) {
      this.$waiting = !!value;
      // we can't just set $waiting variable and use it via ng-disabled in children
      // because in editable-row form is not accessible
      angular.forEach(this.$editables, function(editable) {
        editable.setWaiting(!!value);
      });
    },

    /**
     * Shows error message for particular field.
     * 
     * @method $setError(name, msg)
     * @param {string} name name of field
     * @param {string} msg error message
     * @memberOf editable-form
     */
    $setError: function(name, msg) {
      angular.forEach(this.$editables, function(editable) {
        if(!name || editable.name === name) {
          editable.setError(msg);
        }
      });
    },

    $submit: function() {
      if (this.$waiting) {
        return;
      } 

      //clear errors
      this.$setError(null, '');

      //children onbeforesave
      var pc = editablePromiseCollection();
      angular.forEach(this.$editables, function(editable) {
        pc.when(editable.onbeforesave());
      });

      /*
      onbeforesave result:
      - true/undefined: save data and close form
      - false: close form without saving
      - string: keep form open and show error
      */
      pc.then({
        onWait: angular.bind(this, this.$setWaiting), 
        onTrue: angular.bind(this, checkSelf, true), 
        onFalse: angular.bind(this, checkSelf, false), 
        onString: angular.bind(this, this.$activate)
      });

      //save
      function checkSelf(childrenTrue){
        var pc = editablePromiseCollection();
        pc.when(this.$onbeforesave());
        pc.then({
          onWait: angular.bind(this, this.$setWaiting), 
          onTrue: childrenTrue ? angular.bind(this, this.$save) : angular.bind(this, this.$hide), 
          onFalse: angular.bind(this, this.$hide), 
          onString: angular.bind(this, this.$activate)
        });
      }
    },

    $save: function() {
      // write model for each editable
      angular.forEach(this.$editables, function(editable) {
        editable.save();
      });

      //call onaftersave of self and children
      var pc = editablePromiseCollection();
      pc.when(this.$onaftersave());
      angular.forEach(this.$editables, function(editable) {
        pc.when(editable.onaftersave());
      });

      /*
      onaftersave result:
      - true/undefined/false: just close form
      - string: keep form open and show error
      */
      pc.then({
        onWait: angular.bind(this, this.$setWaiting), 
        onTrue: angular.bind(this, this.$hide), 
        onFalse: angular.bind(this, this.$hide), 
        onString: angular.bind(this, this.$activate)
      });
    },

    $onshow: angular.noop,
    $oncancel: angular.noop,
    $onhide: angular.noop,
    $onbeforesave: angular.noop,
    $onaftersave: angular.noop
  };

  return function() {
    return angular.extend({
      $editables: [],
      /**
       * Form visibility flag.
       * 
       * @var {bool} $visible
       * @memberOf editable-form
       */
      $visible: false,
      /**
       * Form waiting flag. It becomes `true` when form is loading or saving data.
       * 
       * @var {bool} $waiting
       * @memberOf editable-form
       */
      $waiting: false,
      $data: {},
      _clicked: false,
      _blur: null
    }, base);
  };
}]);

/**
 * EditableForm directive. Should be defined in <form> containing editable controls.  
 * It add some usefull methods to form variable exposed to scope by `name="myform"` attribute.
 *
 * @namespace editable-form
 */
angular.module('xeditable').directive('editableForm',
  ['$rootScope', '$parse', 'editableFormController', 'editableOptions',
  function($rootScope, $parse, editableFormController, editableOptions) {
    return {
      restrict: 'A',
      require: ['form'],
      //require: ['form', 'editableForm'],
      //controller: EditableFormController,
      compile: function() {
        return {
          pre: function(scope, elem, attrs, ctrl) {
            var form = ctrl[0];
            var eForm;

            //if `editableForm` has value - publish smartly under this value
            //this is required only for single editor form that is created and removed
            if(attrs.editableForm) {
              if(scope[attrs.editableForm] && scope[attrs.editableForm].$show) {
                eForm = scope[attrs.editableForm];
                angular.extend(form, eForm);
              } else {
                eForm = editableFormController();
                scope[attrs.editableForm] = eForm;
                angular.extend(eForm, form);
              }
            } else { //just merge to form and publish if form has name
              eForm = editableFormController();
              angular.extend(form, eForm);
            }

            //read editables from buffer (that appeared before FORM tag)
            var buf = $rootScope.$$editableBuffer;
            var name = form.$name;
            if(name && buf && buf[name]) {
              angular.forEach(buf[name], function(editable) {
                eForm.$addEditable(editable);
              });
              delete buf[name];
            }
          },
          post: function(scope, elem, attrs, ctrl) {
            var eForm;

            if(attrs.editableForm && scope[attrs.editableForm] && scope[attrs.editableForm].$show) {
              eForm = scope[attrs.editableForm];
            } else {
              eForm = ctrl[0];
            }

            /**
             * Called when form is shown.
             * 
             * @var {method|attribute} onshow 
             * @memberOf editable-form
             */
            if(attrs.onshow) {
              eForm.$onshow = angular.bind(eForm, $parse(attrs.onshow), scope);
            }

            /**
             * Called when form hides after both save or cancel.
             * 
             * @var {method|attribute} onhide 
             * @memberOf editable-form
             */
            if(attrs.onhide) {
              eForm.$onhide = angular.bind(eForm, $parse(attrs.onhide), scope);
            }

            /**
             * Called when form is cancelled.
             * 
             * @var {method|attribute} oncancel
             * @memberOf editable-form
             */
            if(attrs.oncancel) {
              eForm.$oncancel = angular.bind(eForm, $parse(attrs.oncancel), scope);
            }

            /**
             * Whether form initially rendered in shown state.
             *
             * @var {bool|attribute} shown
             * @memberOf editable-form
             */
            if(attrs.shown && $parse(attrs.shown)(scope)) {
              eForm.$show();
            }

            /**
             * Action when form losses focus. Values: `cancel|submit|ignore`.
             * Default is `ignore`.
             * 
             * @var {string|attribute} blur
             * @memberOf editable-form
             */
            eForm._blur = attrs.blur || editableOptions.blurForm;

            // onbeforesave, onaftersave
            if(!attrs.ngSubmit && !attrs.submit) {
              /**
               * Called after all children `onbeforesave` callbacks but before saving form values
               * to model.  
               * If at least one children callback returns `non-string` - it will not not be called.  
               * See [editable-form demo](#editable-form) for details.
               * 
               * @var {method|attribute} onbeforesave
               * @memberOf editable-form
               * 
               */
              if(attrs.onbeforesave) {
                eForm.$onbeforesave = function() {
                  return $parse(attrs.onbeforesave)(scope, {$data: eForm.$data});
                };
              }

              /**
               * Called when form values are saved to model.  
               * See [editable-form demo](#editable-form) for details.
               * 
               * @var {method|attribute} onaftersave 
               * @memberOf editable-form
               * 
               */
              if(attrs.onaftersave) {
                eForm.$onaftersave = function() {
                  return $parse(attrs.onaftersave)(scope, {$data: eForm.$data});
                };
              }

              elem.bind('submit', function(event) {
                event.preventDefault();
                scope.$apply(function() {
                  eForm.$submit();
                });
              });
            }


            // click - mark form as clicked to exclude in document click handler
            elem.bind('click', function(e) {
              // ignore right/middle button click
              if (e.which && e.which !== 1) {
                return;
              }

              if (eForm.$visible) {
                eForm._clicked = true;
              }
            });   

          }
        };
      }
    };
}]);
/**
 * editablePromiseCollection
 *  
 * Collect results of function calls. Shows waiting if there are promises. 
 * Finally, applies callbacks if:
 * - onTrue(): all results are true and all promises resolved to true
 * - onFalse(): at least one result is false or promise resolved to false
 * - onString(): at least one result is string or promise rejected or promise resolved to string
 */

angular.module('xeditable').factory('editablePromiseCollection', ['$q', function($q) { 

  function promiseCollection() {
    return {
      promises: [],
      hasFalse: false,
      hasString: false,
      when: function(result, noPromise) {
        if (result === false) {
          this.hasFalse = true;
        } else if (!noPromise && angular.isObject(result)) {
          this.promises.push($q.when(result));
        } else if (angular.isString(result)){
          this.hasString = true;
        } else { //result === true || result === undefined || result === null
          return;
        }
      },
      //callbacks: onTrue, onFalse, onString
      then: function(callbacks) {
        callbacks = callbacks || {};
        var onTrue = callbacks.onTrue || angular.noop;
        var onFalse = callbacks.onFalse || angular.noop;
        var onString = callbacks.onString || angular.noop;
        var onWait = callbacks.onWait || angular.noop;

        var self = this;

        if (this.promises.length) {
          onWait(true);
          $q.all(this.promises).then(
            //all resolved       
            function(results) {
              onWait(false);
              //check all results via same `when` method (without checking promises)
              angular.forEach(results, function(result) {
                self.when(result, true);  
              });
              applyCallback();
            },
            //some rejected
            function(error) { 
              onWait(false);
              onString();
            }
            );
        } else {
          applyCallback();
        }

        function applyCallback() {
          if (!self.hasString && !self.hasFalse) {
            onTrue();
          } else if (!self.hasString && self.hasFalse) {
            onFalse();
          } else {
            onString();
          }
        }

      }
    };
  }

  return promiseCollection;

}]);

/**
 * editableUtils
 */
 angular.module('xeditable').factory('editableUtils', [function() {
  return {
    indexOf: function (array, obj) {
      if (array.indexOf) return array.indexOf(obj);

      for ( var i = 0; i < array.length; i++) {
        if (obj === array[i]) return i;
      }
      return -1;
    },

    arrayRemove: function (array, value) {
      var index = this.indexOf(array, value);
      if (index >= 0) {
        array.splice(index, 1);
      }
      return value;
    },

    // copy from https://github.com/angular/angular.js/blob/master/src/Angular.js
    camelToDash: function(str) {
      var SNAKE_CASE_REGEXP = /[A-Z]/g;
      return str.replace(SNAKE_CASE_REGEXP, function(letter, pos) {
        return (pos ? '-' : '') + letter.toLowerCase();
      });
    },

    dashToCamel: function(str) {
      var SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
      var MOZ_HACK_REGEXP = /^moz([A-Z])/;
      return str.
      replace(SPECIAL_CHARS_REGEXP, function(_, separator, letter, offset) {
        return offset ? letter.toUpperCase() : letter;
      }).
      replace(MOZ_HACK_REGEXP, 'Moz$1');
    },

    rename: function (tag, el) {
      if (el[0] && el[0].attributes) {
        var newEl = angular.element('<' + tag + '/>');
        newEl.html(el.html());
        var attrs = el[0].attributes;
        for (var i = 0; i < attrs.length; ++i) {
            newEl.attr(attrs.item(i).nodeName, attrs.item(i).value);
        }
        return newEl;
      }
    }
  };
}]);

/**
 * editableNgOptionsParser
 *
 * see: https://github.com/angular/angular.js/blob/master/src/ng/directive/select.js#L131
 */
 angular.module('xeditable').factory('editableNgOptionsParser', [function() {
  //0000111110000000000022220000000000000000000000333300000000000000444444444444444000000000555555555555555000000066666666666666600000000000000007777000000000000000000088888
  var NG_OPTIONS_REGEXP = /^\s*(.*?)(?:\s+as\s+(.*?))?(?:\s+group\s+by\s+(.*))?\s+for\s+(?:([\$\w][\$\w]*)|(?:\(\s*([\$\w][\$\w]*)\s*,\s*([\$\w][\$\w]*)\s*\)))\s+in\s+(.*?)(?:\s+track\s+by\s+(.*?))?$/;

  function parser(optionsExp) {
    var match;

    if (! (match = optionsExp.match(NG_OPTIONS_REGEXP))) {
      throw 'ng-options parse error';
    }

    var 
    displayFn = match[2] || match[1],
    valueName = match[4] || match[6],
    keyName = match[5],
    groupByFn = match[3] || '',
    valueFn = match[2] ? match[1] : valueName,
    valuesFn = match[7],
    track = match[8],
    trackFn = track ? match[8] : null;

    var ngRepeat;
    if (keyName === undefined) { // array
      ngRepeat = valueName + ' in ' + valuesFn;
      if (track !== undefined) {
        ngRepeat += ' track by '+trackFn;
      }
    } else { // object
      ngRepeat = '('+keyName+', '+valueName+') in '+valuesFn;
    }
    
    // group not supported yet
    return {
      ngRepeat: ngRepeat,
      locals: {
        valueName: valueName,
        keyName: keyName,
        valueFn: valueFn,
        displayFn: displayFn
      }
    };
  }

  return parser;
}]);

/**
 * editableCombodate
 *
 * angular version of https://github.com/vitalets/combodate
 */
angular.module('xeditable').factory('editableCombodate', [function() {
  function Combodate(element, options) {
    this.$element = angular.element(element);

    if(this.$element[0].nodeName != 'INPUT') {
      throw 'Combodate should be applied to INPUT element';
    }

    var currentYear = new Date().getFullYear();
    this.defaults = {
      //in this format value stored in original input
      format: 'YYYY-MM-DD HH:mm',
      //in this format items in dropdowns are displayed
      template: 'D / MMM / YYYY   H : mm',
      //initial value, can be `new Date()`
      value: null,
      minYear: 1970,
      maxYear: currentYear,
      yearDescending: true,
      minuteStep: 5,
      secondStep: 1,
      firstItem: 'empty', //'name', 'empty', 'none'
      errorClass: null,
      customClass: '',
      roundTime: true, // whether to round minutes and seconds if step > 1
      smartDays: true // whether days in combo depend on selected month: 31, 30, 28
    };

    this.options = angular.extend({}, this.defaults, options);
    this.init();
  }

  Combodate.prototype = {
    constructor: Combodate,
    init: function () {
      this.map = {
        //key   regexp    moment.method
        day:    ['D',    'date'], 
        month:  ['M',    'month'], 
        year:   ['Y',    'year'], 
        hour:   ['[Hh]', 'hours'],
        minute: ['m',    'minutes'], 
        second: ['s',    'seconds'],
        ampm:   ['[Aa]', ''] 
      };
      
      this.$widget = angular.element('<span class="combodate"></span>').html(this.getTemplate());
      
      this.initCombos();
      
      if (this.options.smartDays) {
        var combo = this;
        this.$widget.find('select').bind('change', function(e) {
          // update days count if month or year changes
          if (angular.element(e.target).hasClass('month') || angular.element(e.target).hasClass('year')) {
            combo.fillCombo('day');
          }
        });        
      }

      this.$widget.find('select').css('width', 'auto');

      // hide original input and insert widget                                       
      this.$element.css('display', 'none').after(this.$widget);
      
      // set initial value
      this.setValue(this.$element.val() || this.options.value);
    },
    
    /*
     Replace tokens in template with <select> elements 
     */         
     getTemplate: function() {
      var tpl = this.options.template;
      var customClass = this.options.customClass;

      //first pass
      angular.forEach(this.map, function(v, k) {
        v = v[0]; 
        var r = new RegExp(v+'+');
        var token = v.length > 1 ? v.substring(1, 2) : v;
        
        tpl = tpl.replace(r, '{'+token+'}');
      });

      //replace spaces with &nbsp;
      tpl = tpl.replace(/ /g, '&nbsp;');

      //second pass
      angular.forEach(this.map, function(v, k) {
        v = v[0];
        var token = v.length > 1 ? v.substring(1, 2) : v;

        tpl = tpl.replace('{'+token+'}', '<select class="'+k+' '+customClass+'"></select>');
      });   

      return tpl;
    },
    
    /*
     Initialize combos that presents in template 
     */        
     initCombos: function() {
      for (var k in this.map) {
        var c = this.$widget[0].querySelectorAll('.'+k);
        // set properties like this.$day, this.$month etc.
        this['$'+k] = c.length ? angular.element(c) : null;
        // fill with items
        this.fillCombo(k);
      }
    },

    /*
     Fill combo with items 
     */        
     fillCombo: function(k) {
      var $combo = this['$'+k];
      if (!$combo) {
        return;
      }

      // define method name to fill items, e.g `fillDays`
      var f = 'fill' + k.charAt(0).toUpperCase() + k.slice(1); 
      var items = this[f]();
      var value = $combo.val();

      $combo.html('');
      for(var i=0; i<items.length; i++) {
        $combo.append('<option value="'+items[i][0]+'">'+items[i][1]+'</option>');
      }

      $combo.val(value);
    },

    /*
     Initialize items of combos. Handles `firstItem` option 
     */
     fillCommon: function(key) {
      var values = [], relTime;

      if(this.options.firstItem === 'name') {
        //need both to support moment ver < 2 and  >= 2
        relTime = moment.relativeTime || moment.langData()._relativeTime; 
        var header = typeof relTime[key] === 'function' ? relTime[key](1, true, key, false) : relTime[key];
        //take last entry (see momentjs lang files structure) 
        header = header.split(' ').reverse()[0];                
        values.push(['', header]);
      } else if(this.options.firstItem === 'empty') {
        values.push(['', '']);
      }
      return values;
    },  


    /*
    fill day
    */
    fillDay: function() {
      var items = this.fillCommon('d'), name, i,
      twoDigit = this.options.template.indexOf('DD') !== -1,
      daysCount = 31;

      // detect days count (depends on month and year)
      // originally https://github.com/vitalets/combodate/pull/7
      if (this.options.smartDays && this.$month && this.$year) {
        var month = parseInt(this.$month.val(), 10);
        var year = parseInt(this.$year.val(), 10);

        if (!isNaN(month) && !isNaN(year)) {
          daysCount = moment([year, month]).daysInMonth();
        }
      }

      for (i = 1; i <= daysCount; i++) {
        name = twoDigit ? this.leadZero(i) : i;
        items.push([i, name]);
      }
      return items;
    },
    
    /*
    fill month
    */
    fillMonth: function() {
      var items = this.fillCommon('M'), name, i, 
      longNames = this.options.template.indexOf('MMMM') !== -1,
      shortNames = this.options.template.indexOf('MMM') !== -1,
      twoDigit = this.options.template.indexOf('MM') !== -1;

      for(i=0; i<=11; i++) {
        if(longNames) {
          //see https://github.com/timrwood/momentjs.com/pull/36
          name = moment().date(1).month(i).format('MMMM');
        } else if(shortNames) {
          name = moment().date(1).month(i).format('MMM');
        } else if(twoDigit) {
          name = this.leadZero(i+1);
        } else {
          name = i+1;
        }
        items.push([i, name]);
      } 
      return items;
    },
    
    /*
    fill year
    */
    fillYear: function() {
      var items = [], name, i, 
      longNames = this.options.template.indexOf('YYYY') !== -1;

      for(i=this.options.maxYear; i>=this.options.minYear; i--) {
        name = longNames ? i : (i+'').substring(2);
        items[this.options.yearDescending ? 'push' : 'unshift']([i, name]);
      }
      
      items = this.fillCommon('y').concat(items);
      
      return items;
    },
    
    /*
    fill hour
    */
    fillHour: function() {
      var items = this.fillCommon('h'), name, i,
      h12 = this.options.template.indexOf('h') !== -1,
      h24 = this.options.template.indexOf('H') !== -1,
      twoDigit = this.options.template.toLowerCase().indexOf('hh') !== -1,
      min = h12 ? 1 : 0, 
      max = h12 ? 12 : 23;

      for(i=min; i<=max; i++) {
        name = twoDigit ? this.leadZero(i) : i;
        items.push([i, name]);
      } 
      return items;
    },

    /*
    fill minute
    */
    fillMinute: function() {
      var items = this.fillCommon('m'), name, i,
      twoDigit = this.options.template.indexOf('mm') !== -1;

      for(i=0; i<=59; i+= this.options.minuteStep) {
        name = twoDigit ? this.leadZero(i) : i;
        items.push([i, name]);
      }
      return items;
    },
    
    /*
    fill second
    */
    fillSecond: function() {
      var items = this.fillCommon('s'), name, i,
      twoDigit = this.options.template.indexOf('ss') !== -1;

      for(i=0; i<=59; i+= this.options.secondStep) {
        name = twoDigit ? this.leadZero(i) : i;
        items.push([i, name]);
      }    
      return items;
    },
    
    /*
    fill ampm
    */
    fillAmpm: function() {
      var ampmL = this.options.template.indexOf('a') !== -1,
      ampmU = this.options.template.indexOf('A') !== -1,            
      items = [
      ['am', ampmL ? 'am' : 'AM'],
      ['pm', ampmL ? 'pm' : 'PM']
      ];
      return items;
    },

    /*
     Returns current date value from combos. 
     If format not specified - `options.format` used.
     If format = `null` - Moment object returned.
     */
     getValue: function(format) {
      var dt, values = {}, 
      that = this,
      notSelected = false;

      //getting selected values    
      angular.forEach(this.map, function(v, k) {
        if(k === 'ampm') {
          return;
        }
        var def = k === 'day' ? 1 : 0;

        values[k] = that['$'+k] ? parseInt(that['$'+k].val(), 10) : def; 
        
        if(isNaN(values[k])) {
         notSelected = true;
         return false; 
       }
     });
      
      //if at least one visible combo not selected - return empty string
      if(notSelected) {
       return '';
     }

      //convert hours 12h --> 24h 
      if(this.$ampm) {
        //12:00 pm --> 12:00 (24-h format, midday), 12:00 am --> 00:00 (24-h format, midnight, start of day)
        if(values.hour === 12) {
          values.hour = this.$ampm.val() === 'am' ? 0 : 12;                    
        } else {
          values.hour = this.$ampm.val() === 'am' ? values.hour : values.hour+12;
        }
      }
      
      dt = moment([values.year, values.month, values.day, values.hour, values.minute, values.second]);
      
      //highlight invalid date
      this.highlight(dt);

      format = format === undefined ? this.options.format : format;
      if(format === null) {
       return dt.isValid() ? dt : null; 
     } else {
       return dt.isValid() ? dt.format(format) : ''; 
     }
   },

   setValue: function(value) {
    if(!value) {
      return;
    }

      // parse in strict mode (third param `true`)
      var dt = typeof value === 'string' ? moment(value, this.options.format, true) : moment(value),
      that = this,
      values = {};
      
      //function to find nearest value in select options
      function getNearest($select, value) {
        var delta = {};
        angular.forEach($select.children('option'), function(opt, i){
          var optValue = angular.element(opt).attr('value');

          if(optValue === '') return;
          var distance = Math.abs(optValue - value); 
          if(typeof delta.distance === 'undefined' || distance < delta.distance) {
            delta = {value: optValue, distance: distance};
          } 
        }); 
        return delta.value;
      }
      
      if(dt.isValid()) {
        //read values from date object
        angular.forEach(this.map, function(v, k) {
          if(k === 'ampm') {
            return; 
          }
          values[k] = dt[v[1]]();
        });

        if(this.$ampm) {
          //12:00 pm --> 12:00 (24-h format, midday), 12:00 am --> 00:00 (24-h format, midnight, start of day)
          if(values.hour >= 12) {
            values.ampm = 'pm';
            if(values.hour > 12) {
              values.hour -= 12;
            }
          } else {
            values.ampm = 'am';
            if(values.hour === 0) {
              values.hour = 12;
            }
          }
        }

        angular.forEach(values, function(v, k) {
          //call val() for each existing combo, e.g. this.$hour.val()
          if(that['$'+k]) {

            if(k === 'minute' && that.options.minuteStep > 1 && that.options.roundTime) {
             v = getNearest(that['$'+k], v);
           }
           
           if(k === 'second' && that.options.secondStep > 1 && that.options.roundTime) {
             v = getNearest(that['$'+k], v);
           }                       
           
           that['$'+k].val(v);
         }
       });

        // update days count
        if (this.options.smartDays) {
          this.fillCombo('day');
        }

        this.$element.val(dt.format(this.options.format)).triggerHandler('change');
      }
    },
    
    /*
     highlight combos if date is invalid
     */
     highlight: function(dt) {
      if(!dt.isValid()) {
        if(this.options.errorClass) {
          this.$widget.addClass(this.options.errorClass);
        } else {
          //store original border color
          if(!this.borderColor) {
            this.borderColor = this.$widget.find('select').css('border-color'); 
          }
          this.$widget.find('select').css('border-color', 'red');
        }
      } else {
        if(this.options.errorClass) {
          this.$widget.removeClass(this.options.errorClass);
        } else {
          this.$widget.find('select').css('border-color', this.borderColor);
        }  
      }
    },
    
    leadZero: function(v) {
      return v <= 9 ? '0' + v : v; 
    },
    
    destroy: function() {
      this.$widget.remove();
      this.$element.removeData('combodate').show();
    }

  };

  return {
    getInstance: function(element, options) {
      return new Combodate(element, options);
    }
  };
}]);

/*
Editable icons:
- default
- font-awesome

*/
angular.module('xeditable').factory('editableIcons', function() {

  var icons = {
    //Icon-set to use, defaults to bootstrap icons
    default: {
      'bs2': {
        ok: 'icon-ok icon-white',
        cancel: 'icon-remove',
        clear: 'icon-trash'
      },
      'bs3': {
        ok: 'glyphicon glyphicon-ok',
        cancel: 'glyphicon glyphicon-remove',
        clear: 'glyphicon glyphicon-trash'
      }
    },
    external: {
      'font-awesome': {
        ok: 'fa fa-check',
        cancel: 'fa fa-times',
        clear: 'fa fa-trash'
      }
    }
  };

  return icons;
});

/* jshint -W086 */
/*
Editable themes:
- default
- bootstrap 2
- bootstrap 3
- semantic-ui

Note: in postrender() `this` is instance of editableController
*/
angular.module('xeditable').factory('editableThemes', function() {
  var themes = {
    //default
    'default': {
      formTpl:      '<form class="editable-wrap"></form>',
      noformTpl:    '<span class="editable-wrap"></span>',
      controlsTpl:  '<span class="editable-controls"></span>',
      inputTpl:     '',
      errorTpl:     '<div class="editable-error" data-ng-if="$error" data-ng-bind-html="$error"></div>',
      buttonsTpl:   '<span class="editable-buttons"></span>',
      submitTpl:    '<button type="submit">save</button>',
      cancelTpl:    '<button type="button" ng-click="$form.$cancel()">cancel</button>',
      resetTpl:    '<button type="reset">clear</button>'
    },

    //bs2
    'bs2': {
      formTpl:     '<form class="form-inline editable-wrap" role="form"></form>',
      noformTpl:   '<span class="editable-wrap"></span>',
      controlsTpl: '<div class="editable-controls controls control-group" ng-class="{\'error\': $error}"></div>',
      inputTpl:    '',
      errorTpl:    '<div class="editable-error help-block" data-ng-if="$error" data-ng-bind-html="$error"></div>',
      buttonsTpl:  '<span class="editable-buttons"></span>',
      submitTpl:   '<button type="submit" class="btn btn-primary"><span></span></button>',
      cancelTpl:   '<button type="button" class="btn" ng-click="$form.$cancel()">'+
                      '<span></span>'+
                   '</button>',
      resetTpl:    '<button type="reset" class="btn btn-danger">clear</button>'

    },

    //bs3
    'bs3': {
      formTpl:     '<form class="form-inline editable-wrap" role="form"></form>',
      noformTpl:   '<span class="editable-wrap"></span>',
      controlsTpl: '<div class="editable-controls form-group" ng-class="{\'has-error\': $error}"></div>',
      inputTpl:    '',
      errorTpl:    '<div class="editable-error help-block" data-ng-if="$error" data-ng-bind-html="$error"></div>',
      buttonsTpl:  '<span class="editable-buttons"></span>',
      submitTpl:   '<button type="submit" class="btn btn-primary"><span></span></button>',
      cancelTpl:   '<button type="button" class="btn btn-default" ng-click="$form.$cancel()">'+
                     '<span></span>'+
                   '</button>',
      resetTpl:    '<button type="reset" class="btn btn-danger">clear</button>',

      //bs3 specific prop to change buttons class: btn-sm, btn-lg
      buttonsClass: '',
      //bs3 specific prop to change standard inputs class: input-sm, input-lg
      inputClass: '',
      postrender: function() {
        //apply `form-control` class to std inputs
        switch(this.directiveName) {
          case 'editableText':
          case 'editableSelect':
          case 'editableTextarea':
          case 'editableEmail':
          case 'editableTel':
          case 'editableNumber':
          case 'editableUrl':
          case 'editableSearch':
          case 'editableDate':
          case 'editableDatetime':
          case 'editableBsdate':
          case 'editableTime':
          case 'editableMonth':
          case 'editableWeek':
          case 'editablePassword':
          case 'editableDatetimeLocal':
            this.inputEl.addClass('form-control');
            if(this.theme.inputClass) {
              // don`t apply `input-sm` and `input-lg` to select multiple
              // should be fixed in bs itself!
              if(this.inputEl.attr('multiple') &&
                (this.theme.inputClass === 'input-sm' || this.theme.inputClass === 'input-lg')) {
                  break;
              }
              this.inputEl.addClass(this.theme.inputClass);
            }
          break;
          case 'editableCheckbox':
              this.editorEl.addClass('checkbox');
        }

        //apply buttonsClass (bs3 specific!)
        if(this.buttonsEl && this.theme.buttonsClass) {
          this.buttonsEl.find('button').addClass(this.theme.buttonsClass);
        }
      }
    },
    
    //semantic-ui
    'semantic': {
      formTpl:     '<form class="editable-wrap ui form" ng-class="{\'error\': $error}" role="form"></form>',
      noformTpl:   '<span class="editable-wrap"></span>',
      controlsTpl: '<div class="editable-controls ui fluid input" ng-class="{\'error\': $error}"></div>',
      inputTpl:    '',
      errorTpl:    '<div class="editable-error ui error message" data-ng-if="$error" data-ng-bind-html="$error"></div>',
      buttonsTpl:  '<span class="mini ui buttons"></span>',
      submitTpl:   '<button type="submit" class="ui primary button"><i class="ui check icon"></i></button>',
      cancelTpl:   '<button type="button" class="ui button" ng-click="$form.$cancel()">'+
                      '<i class="ui cancel icon"></i>'+
                   '</button>',
      resetTpl:    '<button type="reset" class="ui button">clear</button>'
    }
  };

  return themes;
});

/**
 * @license Highcharts JS v5.0.4 (2016-11-22)
 *
 * (c) 2009-2016 Torstein Honsi
 *
 * License: www.highcharts.com/license
 */
'use strict';
(function(root, factory) {
    if (typeof module === 'object' && module.exports) {
        module.exports = root.document ?
            factory(root) :
            factory;
    } else {
        root.Highcharts = factory(root);
    }
}(typeof window !== 'undefined' ? window : this, function(win) {
    var Highcharts = (function() {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        /* global window */
        var win = window,
            doc = win.document;

        var SVG_NS = 'http://www.w3.org/2000/svg',
            userAgent = (win.navigator && win.navigator.userAgent) || '',
            svg = doc && doc.createElementNS && !!doc.createElementNS(SVG_NS, 'svg').createSVGRect,
            isMS = /(edge|msie|trident)/i.test(userAgent) && !window.opera,
            vml = !svg,
            isFirefox = /Firefox/.test(userAgent),
            hasBidiBug = isFirefox && parseInt(userAgent.split('Firefox/')[1], 10) < 4; // issue #38

        var Highcharts = win.Highcharts ? win.Highcharts.error(16, true) : {
            product: 'Highcharts',
            version: '5.0.4',
            deg2rad: Math.PI * 2 / 360,
            doc: doc,
            hasBidiBug: hasBidiBug,
            hasTouch: doc && doc.documentElement.ontouchstart !== undefined,
            isMS: isMS,
            isWebKit: /AppleWebKit/.test(userAgent),
            isFirefox: isFirefox,
            isTouchDevice: /(Mobile|Android|Windows Phone)/.test(userAgent),
            SVG_NS: SVG_NS,
            chartCount: 0,
            seriesTypes: {},
            symbolSizes: {},
            svg: svg,
            vml: vml,
            win: win,
            charts: [],
            marginNames: ['plotTop', 'marginRight', 'marginBottom', 'plotLeft'],
            noop: function() {
                return undefined;
            }
        };
        return Highcharts;
    }());
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        /* eslint max-len: ["warn", 80, 4] */
        'use strict';

        /**
         * The Highcharts object is the placeholder for all other members, and various
         * utility functions.
         * @namespace Highcharts
         */

        var timers = [];

        var charts = H.charts,
            doc = H.doc,
            win = H.win;

        /**
         * Provide error messages for debugging, with links to online explanation. This
         * function can be overridden to provide custom error handling.
         *
         * @function #error
         * @memberOf Highcharts
         * @param {Number} code - The error code. See [errors.xml]{@link 
         *     https://github.com/highcharts/highcharts/blob/master/errors/errors.xml}
         *     for available codes.
         * @param {Boolean} [stop=false] - Whether to throw an error or just log a 
         *     warning in the console.
         */
        H.error = function(code, stop) {
            var msg = 'Highcharts error #' + code + ': www.highcharts.com/errors/' +
                code;
            if (stop) {
                throw new Error(msg);
            }
            // else ...
            if (win.console) {
                console.log(msg); // eslint-disable-line no-console
            }
        };

        /**
         * An animator object. One instance applies to one property (attribute or style
         * prop) on one element.
         *
         * @constructor Fx
         * @memberOf Highcharts
         * @param {HTMLDOMElement|SVGElement} elem - The element to animate.
         * @param {AnimationOptions} options - Animation options.
         * @param {string} prop - The single attribute or CSS property to animate.
         */
        H.Fx = function(elem, options, prop) {
            this.options = options;
            this.elem = elem;
            this.prop = prop;
        };
        H.Fx.prototype = {

            /**
             * Set the current step of a path definition on SVGElement.
             *
             * @function #dSetter
             * @memberOf Highcharts.Fx
             */
            dSetter: function() {
                var start = this.paths[0],
                    end = this.paths[1],
                    ret = [],
                    now = this.now,
                    i = start.length,
                    startVal;

                // Land on the final path without adjustment points appended in the ends
                if (now === 1) {
                    ret = this.toD;

                } else if (i === end.length && now < 1) {
                    while (i--) {
                        startVal = parseFloat(start[i]);
                        ret[i] =
                            isNaN(startVal) ? // a letter instruction like M or L
                            start[i] :
                            now * (parseFloat(end[i] - startVal)) + startVal;

                    }
                    // If animation is finished or length not matching, land on right value
                } else {
                    ret = end;
                }
                this.elem.animProp = 'd';
                this.elem.attr('d', ret);
                this.elem.animProp = null;
            },

            /**
             * Update the element with the current animation step.
             *
             * @function #update
             * @memberOf Highcharts.Fx
             */
            update: function() {
                var elem = this.elem,
                    prop = this.prop, // if destroyed, it is null
                    now = this.now,
                    step = this.options.step;

                // Animation setter defined from outside
                if (this[prop + 'Setter']) {
                    this[prop + 'Setter']();

                    // Other animations on SVGElement
                } else if (elem.attr) {
                    if (elem.element) {
                        elem.animProp = prop;
                        elem.attr(prop, now);
                        elem.animProp = null;
                    }

                    // HTML styles, raw HTML content like container size
                } else {
                    elem.style[prop] = now + this.unit;
                }

                if (step) {
                    step.call(elem, now, this);
                }

            },

            /**
             * Run an animation.
             *
             * @function #run
             * @memberOf Highcharts.Fx
             * @param {Number} from - The current value, value to start from.
             * @param {Number} to - The end value, value to land on.
             * @param {String} [unit] - The property unit, for example `px`.
             * @returns {void}
             */
            run: function(from, to, unit) {
                var self = this,
                    timer = function(gotoEnd) {
                        return timer.stopped ? false : self.step(gotoEnd);
                    },
                    i;

                this.startTime = +new Date();
                this.start = from;
                this.end = to;
                this.unit = unit;
                this.now = this.start;
                this.pos = 0;

                timer.elem = this.elem;
                timer.prop = this.prop;

                if (timer() && timers.push(timer) === 1) {
                    timer.timerId = setInterval(function() {

                        for (i = 0; i < timers.length; i++) {
                            if (!timers[i]()) {
                                timers.splice(i--, 1);
                            }
                        }

                        if (!timers.length) {
                            clearInterval(timer.timerId);
                        }
                    }, 13);
                }
            },

            /**
             * Run a single step in the animation.
             *
             * @function #step
             * @memberOf Highcharts.Fx
             * @param   {Boolean} [gotoEnd] - Whether to go to the endpoint of the
             *     animation after abort.
             * @returns {Boolean} Returns `true` if animation continues.
             */
            step: function(gotoEnd) {
                var t = +new Date(),
                    ret,
                    done,
                    options = this.options,
                    elem = this.elem,
                    complete = options.complete,
                    duration = options.duration,
                    curAnim = options.curAnim,
                    i;

                if (elem.attr && !elem.element) { // #2616, element is destroyed
                    ret = false;

                } else if (gotoEnd || t >= duration + this.startTime) {
                    this.now = this.end;
                    this.pos = 1;
                    this.update();

                    curAnim[this.prop] = true;

                    done = true;
                    for (i in curAnim) {
                        if (curAnim[i] !== true) {
                            done = false;
                        }
                    }

                    if (done && complete) {
                        complete.call(elem);
                    }
                    ret = false;

                } else {
                    this.pos = options.easing((t - this.startTime) / duration);
                    this.now = this.start + ((this.end - this.start) * this.pos);
                    this.update();
                    ret = true;
                }
                return ret;
            },

            /**
             * Prepare start and end values so that the path can be animated one to one.
             *
             * @function #initPath
             * @memberOf Highcharts.Fx
             * @param {SVGElement} elem - The SVGElement item.
             * @param {String} fromD - Starting path definition.
             * @param {Array} toD - Ending path definition.
             * @returns {Array} An array containing start and end paths in array form
             * so that they can be animated in parallel.
             */
            initPath: function(elem, fromD, toD) {
                fromD = fromD || '';
                var shift,
                    startX = elem.startX,
                    endX = elem.endX,
                    bezier = fromD.indexOf('C') > -1,
                    numParams = bezier ? 7 : 3,
                    fullLength,
                    slice,
                    i,
                    start = fromD.split(' '),
                    end = toD.slice(), // copy
                    isArea = elem.isArea,
                    positionFactor = isArea ? 2 : 1,
                    reverse;

                /**
                 * In splines make moveTo and lineTo points have six parameters like
                 * bezier curves, to allow animation one-to-one.
                 */
                function sixify(arr) {
                    var isOperator,
                        nextIsOperator;
                    i = arr.length;
                    while (i--) {

                        // Fill in dummy coordinates only if the next operator comes
                        // three places behind (#5788)
                        isOperator = arr[i] === 'M' || arr[i] === 'L';
                        nextIsOperator = /[a-zA-Z]/.test(arr[i + 3]);
                        if (isOperator && !nextIsOperator) {
                            arr.splice(
                                i + 1, 0,
                                arr[i + 1], arr[i + 2],
                                arr[i + 1], arr[i + 2]
                            );
                        }
                    }
                }

                /**
                 * Insert an array at the given position of another array
                 */
                function insertSlice(arr, subArr, index) {
                    [].splice.apply(
                        arr, [index, 0].concat(subArr)
                    );
                }

                /**
                 * If shifting points, prepend a dummy point to the end path. 
                 */
                function prepend(arr, other) {
                    while (arr.length < fullLength) {

                        // Move to, line to or curve to?
                        arr[0] = other[fullLength - arr.length];

                        // Prepend a copy of the first point
                        insertSlice(arr, arr.slice(0, numParams), 0);

                        // For areas, the bottom path goes back again to the left, so we
                        // need to append a copy of the last point.
                        if (isArea) {
                            insertSlice(
                                arr,
                                arr.slice(arr.length - numParams), arr.length
                            );
                            i--;
                        }
                    }
                    arr[0] = 'M';
                }

                /**
                 * Copy and append last point until the length matches the end length
                 */
                function append(arr, other) {
                    var i = (fullLength - arr.length) / numParams;
                    while (i > 0 && i--) {

                        // Pull out the slice that is going to be appended or inserted.
                        // In a line graph, the positionFactor is 1, and the last point
                        // is sliced out. In an area graph, the positionFactor is 2,
                        // causing the middle two points to be sliced out, since an area
                        // path starts at left, follows the upper path then turns and
                        // follows the bottom back. 
                        slice = arr.slice().splice(
                            (arr.length / positionFactor) - numParams,
                            numParams * positionFactor
                        );

                        // Move to, line to or curve to?
                        slice[0] = other[fullLength - numParams - (i * numParams)];

                        // Disable first control point
                        if (bezier) {
                            slice[numParams - 6] = slice[numParams - 2];
                            slice[numParams - 5] = slice[numParams - 1];
                        }

                        // Now insert the slice, either in the middle (for areas) or at
                        // the end (for lines)
                        insertSlice(arr, slice, arr.length / positionFactor);

                        if (isArea) {
                            i--;
                        }
                    }
                }

                if (bezier) {
                    sixify(start);
                    sixify(end);
                }

                // For sideways animation, find out how much we need to shift to get the
                // start path Xs to match the end path Xs.
                if (startX && endX) {
                    for (i = 0; i < startX.length; i++) {
                        // Moving left, new points coming in on right
                        if (startX[i] === endX[0]) {
                            shift = i;
                            break;
                            // Moving right
                        } else if (startX[0] ===
                            endX[endX.length - startX.length + i]) {
                            shift = i;
                            reverse = true;
                            break;
                        }
                    }
                    if (shift === undefined) {
                        start = [];
                    }
                }

                if (start.length && H.isNumber(shift)) {

                    // The common target length for the start and end array, where both 
                    // arrays are padded in opposite ends
                    fullLength = end.length + shift * positionFactor * numParams;

                    if (!reverse) {
                        prepend(end, start);
                        append(start, end);
                    } else {
                        prepend(start, end);
                        append(end, start);
                    }
                }

                return [start, end];
            }
        }; // End of Fx prototype


        /**
         * Utility function to extend an object with the members of another.
         *
         * @function #extend
         * @memberOf Highcharts
         * @param {Object} a - The object to be extended.
         * @param {Object} b - The object to add to the first one.
         * @returns {Object} Object a, the original object.
         */
        H.extend = function(a, b) {
            var n;
            if (!a) {
                a = {};
            }
            for (n in b) {
                a[n] = b[n];
            }
            return a;
        };

        /**
         * Utility function to deep merge two or more objects and return a third object.
         * If the first argument is true, the contents of the second object is copied
         * into the first object. The merge function can also be used with a single 
         * object argument to create a deep copy of an object.
         *
         * @function #merge
         * @memberOf Highcharts
         * @param {Boolean} [extend] - Whether to extend the left-side object (a) or
                  return a whole new object.
         * @param {Object} a - The first object to extend. When only this is given, the
                  function returns a deep copy.
         * @param {...Object} [n] - An object to merge into the previous one.
         * @returns {Object} - The merged object. If the first argument is true, the 
         * return is the same as the second argument.
         */
        H.merge = function() {
            var i,
                args = arguments,
                len,
                ret = {},
                doCopy = function(copy, original) {
                    var value, key;

                    // An object is replacing a primitive
                    if (typeof copy !== 'object') {
                        copy = {};
                    }

                    for (key in original) {
                        if (original.hasOwnProperty(key)) {
                            value = original[key];

                            // Copy the contents of objects, but not arrays or DOM nodes
                            if (H.isObject(value, true) &&
                                key !== 'renderTo' &&
                                typeof value.nodeType !== 'number') {
                                copy[key] = doCopy(copy[key] || {}, value);

                                // Primitives and arrays are copied over directly
                            } else {
                                copy[key] = original[key];
                            }
                        }
                    }
                    return copy;
                };

            // If first argument is true, copy into the existing object. Used in
            // setOptions.
            if (args[0] === true) {
                ret = args[1];
                args = Array.prototype.slice.call(args, 2);
            }

            // For each argument, extend the return
            len = args.length;
            for (i = 0; i < len; i++) {
                ret = doCopy(ret, args[i]);
            }

            return ret;
        };

        /**
         * Shortcut for parseInt
         * @ignore
         * @param {Object} s
         * @param {Number} mag Magnitude
         */
        H.pInt = function(s, mag) {
            return parseInt(s, mag || 10);
        };

        /**
         * Utility function to check for string type.
         *
         * @function #isString
         * @memberOf Highcharts
         * @param {Object} s - The item to check.
         * @returns {Boolean} - True if the argument is a string.
         */
        H.isString = function(s) {
            return typeof s === 'string';
        };

        /**
         * Utility function to check if an item is an array.
         *
         * @function #isArray
         * @memberOf Highcharts
         * @param {Object} obj - The item to check.
         * @returns {Boolean} - True if the argument is an array.
         */
        H.isArray = function(obj) {
            var str = Object.prototype.toString.call(obj);
            return str === '[object Array]' || str === '[object Array Iterator]';
        };

        /**
         * Utility function to check if an item is of type object.
         *
         * @function #isObject
         * @memberOf Highcharts
         * @param {Object} obj - The item to check.
         * @param {Boolean} [strict=false] - Also checks that the object is not an
         *    array.
         * @returns {Boolean} - True if the argument is an object.
         */
        H.isObject = function(obj, strict) {
            return obj && typeof obj === 'object' && (!strict || !H.isArray(obj));
        };

        /**
         * Utility function to check if an item is of type number.
         *
         * @function #isNumber
         * @memberOf Highcharts
         * @param {Object} n - The item to check.
         * @returns {Boolean} - True if the item is a number and is not NaN.
         */
        H.isNumber = function(n) {
            return typeof n === 'number' && !isNaN(n);
        };

        /**
         * Remove the last occurence of an item from an array.
         *
         * @function #erase
         * @memberOf Highcharts
         * @param {Array} arr - The array.
         * @param {*} item - The item to remove.
         */
        H.erase = function(arr, item) {
            var i = arr.length;
            while (i--) {
                if (arr[i] === item) {
                    arr.splice(i, 1);
                    break;
                }
            }
        };

        /**
         * Check if an object is null or undefined.
         *
         * @function #defined
         * @memberOf Highcharts
         * @param {Object} obj - The object to check.
         * @returns {Boolean} - False if the object is null or undefined, otherwise
         *        true.
         */
        H.defined = function(obj) {
            return obj !== undefined && obj !== null;
        };

        /**
         * Set or get an attribute or an object of attributes. To use as a setter, pass
         * a key and a value, or let the second argument be a collection of keys and
         * values. To use as a getter, pass only a string as the second argument.
         *
         * @function #attr
         * @memberOf Highcharts
         * @param {Object} elem - The DOM element to receive the attribute(s).
         * @param {String|Object} [prop] - The property or an object of key-value pairs.
         * @param {String} [value] - The value if a single property is set.
         * @returns {*} When used as a getter, return the value.
         */
        H.attr = function(elem, prop, value) {
            var key,
                ret;

            // if the prop is a string
            if (H.isString(prop)) {
                // set the value
                if (H.defined(value)) {
                    elem.setAttribute(prop, value);

                    // get the value
                } else if (elem && elem.getAttribute) {
                    ret = elem.getAttribute(prop);
                }

                // else if prop is defined, it is a hash of key/value pairs
            } else if (H.defined(prop) && H.isObject(prop)) {
                for (key in prop) {
                    elem.setAttribute(key, prop[key]);
                }
            }
            return ret;
        };

        /**
         * Check if an element is an array, and if not, make it into an array.
         *
         * @function #splat
         * @memberOf Highcharts
         * @param obj {*} - The object to splat.
         * @returns {Array} The produced or original array.
         */
        H.splat = function(obj) {
            return H.isArray(obj) ? obj : [obj];
        };

        /**
         * Set a timeout if the delay is given, otherwise perform the function
         * synchronously.
         *
         * @function #syncTimeout
         * @memberOf Highcharts
         * @param   {Function} fn - The function callback.
         * @param   {Number}   delay - Delay in milliseconds.
         * @param   {Object}   [context] - The context.
         * @returns {Number} An identifier for the timeout that can later be cleared
         * with clearTimeout.
         */
        H.syncTimeout = function(fn, delay, context) {
            if (delay) {
                return setTimeout(fn, delay, context);
            }
            fn.call(0, context);
        };


        /**
         * Return the first value that is not null or undefined.
         *
         * @function #pick
         * @memberOf Highcharts
         * @param {...*} items - Variable number of arguments to inspect.
         * @returns {*} The value of the first argument that is not null or undefined.
         */
        H.pick = function() {
            var args = arguments,
                i,
                arg,
                length = args.length;
            for (i = 0; i < length; i++) {
                arg = args[i];
                if (arg !== undefined && arg !== null) {
                    return arg;
                }
            }
        };

        /**
         * @typedef {Object} CSSObject - A style object with camel case property names.
         * The properties can be whatever styles are supported on the given SVG or HTML
         * element.
         * @example
         * {
         *    fontFamily: 'monospace',
         *    fontSize: '1.2em'
         * }
         */
        /**
         * Set CSS on a given element.
         *
         * @function #css
         * @memberOf Highcharts
         * @param {HTMLDOMElement} el - A HTML DOM element.
         * @param {CSSObject} styles - Style object with camel case property names.
         * @returns {void}
         */
        H.css = function(el, styles) {
            if (H.isMS && !H.svg) { // #2686
                if (styles && styles.opacity !== undefined) {
                    styles.filter = 'alpha(opacity=' + (styles.opacity * 100) + ')';
                }
            }
            H.extend(el.style, styles);
        };

        /**
         * A HTML DOM element.
         * @typedef {Object} HTMLDOMElement
         */

        /**
         * Utility function to create an HTML element with attributes and styles.
         *
         * @function #createElement
         * @memberOf Highcharts
         * @param {String} tag - The HTML tag.
         * @param {Object} [attribs] - Attributes as an object of key-value pairs.
         * @param {CSSObject} [styles] - Styles as an object of key-value pairs.
         * @param {Object} [parent] - The parent HTML object.
         * @param {Boolean} [nopad=false] - If true, remove all padding, border and
         *    margin.
         * @returns {HTMLDOMElement} The created DOM element.
         */
        H.createElement = function(tag, attribs, styles, parent, nopad) {
            var el = doc.createElement(tag),
                css = H.css;
            if (attribs) {
                H.extend(el, attribs);
            }
            if (nopad) {
                css(el, {
                    padding: 0,
                    border: 'none',
                    margin: 0
                });
            }
            if (styles) {
                css(el, styles);
            }
            if (parent) {
                parent.appendChild(el);
            }
            return el;
        };

        /**
         * Extend a prototyped class by new members.
         *
         * @function #extendClass
         * @memberOf Highcharts
         * @param {Object} parent - The parent prototype to inherit.
         * @param {Object} members - A collection of prototype members to add or
         *        override compared to the parent prototype.
         * @returns {Object} A new prototype.
         */
        H.extendClass = function(parent, members) {
            var object = function() {};
            object.prototype = new parent(); // eslint-disable-line new-cap
            H.extend(object.prototype, members);
            return object;
        };

        /**
         * Left-pad a string to a given length by adding a character repetetively.
         *
         * @function #pad
         * @memberOf Highcharts
         * @param {Number} number - The input string or number.
         * @param {Number} length - The desired string length.
         * @param {String} [padder=0] - The character to pad with.
         * @returns {String} The padded string.
         */
        H.pad = function(number, length, padder) {
            return new Array((length || 2) + 1 -
                String(number).length).join(padder || 0) + number;
        };

        /**
         * @typedef {Number|String} RelativeSize - If a number is given, it defines the
         *    pixel length. If a percentage string is given, like for example `'50%'`,
         *    the setting defines a length relative to a base size, for example the size
         *    of a container.
         */
        /**
         * Return a length based on either the integer value, or a percentage of a base.
         *
         * @function #relativeLength
         * @memberOf Highcharts
         * @param {RelativeSize} value - A percentage string or a number.
         * @param {Number} base - The full length that represents 100%.
         * @returns {Number} The computed length.
         */
        H.relativeLength = function(value, base) {
            return (/%$/).test(value) ?
                base * parseFloat(value) / 100 :
                parseFloat(value);
        };

        /**
         * Wrap a method with extended functionality, preserving the original function.
         *
         * @function #wrap
         * @memberOf Highcharts
         * @param {Object} obj - The context object that the method belongs to. In real
         *        cases, this is often a prototype.
         * @param {String} method - The name of the method to extend.
         * @param {Function} func - A wrapper function callback. This function is called
         *        with the same arguments as the original function, except that the
         *        original function is unshifted and passed as the first argument.
         * @returns {void}
         */
        H.wrap = function(obj, method, func) {
            var proceed = obj[method];
            obj[method] = function() {
                var args = Array.prototype.slice.call(arguments),
                    outerArgs = arguments,
                    ctx = this,
                    ret;
                ctx.proceed = function() {
                    proceed.apply(ctx, arguments.length ? arguments : outerArgs);
                };
                args.unshift(proceed);
                ret = func.apply(this, args);
                ctx.proceed = null;
                return ret;
            };
        };

        /**
         * Get the time zone offset based on the current timezone information as set in
         * the global options.
         *
         * @function #getTZOffset
         * @memberOf Highcharts
         * @param  {Number} timestamp - The JavaScript timestamp to inspect.
         * @return {Number} - The timezone offset in minutes compared to UTC.
         */
        H.getTZOffset = function(timestamp) {
            var d = H.Date;
            return ((d.hcGetTimezoneOffset && d.hcGetTimezoneOffset(timestamp)) ||
                d.hcTimezoneOffset || 0) * 60000;
        };

        /**
         * Format a date, based on the syntax for PHP's [strftime]{@link
         * http://www.php.net/manual/en/function.strftime.php} function.
         *
         * @function #dateFormat
         * @memberOf Highcharts
         * @param {String} format - The desired format where various time
         *        representations are prefixed with %.
         * @param {Number} timestamp - The JavaScript timestamp.
         * @param {Boolean} [capitalize=false] - Upper case first letter in the return.
         * @returns {String} The formatted date.
         */
        H.dateFormat = function(format, timestamp, capitalize) {
            if (!H.defined(timestamp) || isNaN(timestamp)) {
                return H.defaultOptions.lang.invalidDate || '';
            }
            format = H.pick(format, '%Y-%m-%d %H:%M:%S');

            var D = H.Date,
                date = new D(timestamp - H.getTZOffset(timestamp)),
                key, // used in for constuct below
                // get the basic time values
                hours = date[D.hcGetHours](),
                day = date[D.hcGetDay](),
                dayOfMonth = date[D.hcGetDate](),
                month = date[D.hcGetMonth](),
                fullYear = date[D.hcGetFullYear](),
                lang = H.defaultOptions.lang,
                langWeekdays = lang.weekdays,
                shortWeekdays = lang.shortWeekdays,
                pad = H.pad,

                // List all format keys. Custom formats can be added from the outside. 
                replacements = H.extend({

                    //-- Day
                    // Short weekday, like 'Mon'
                    'a': shortWeekdays ?
                        shortWeekdays[day] : langWeekdays[day].substr(0, 3),
                    // Long weekday, like 'Monday'
                    'A': langWeekdays[day],
                    // Two digit day of the month, 01 to 31
                    'd': pad(dayOfMonth),
                    // Day of the month, 1 through 31
                    'e': pad(dayOfMonth, 2, ' '),
                    'w': day,

                    // Week (none implemented)
                    //'W': weekNumber(),

                    //-- Month
                    // Short month, like 'Jan'
                    'b': lang.shortMonths[month],
                    // Long month, like 'January'
                    'B': lang.months[month],
                    // Two digit month number, 01 through 12
                    'm': pad(month + 1),

                    //-- Year
                    // Two digits year, like 09 for 2009
                    'y': fullYear.toString().substr(2, 2),
                    // Four digits year, like 2009
                    'Y': fullYear,

                    //-- Time
                    // Two digits hours in 24h format, 00 through 23
                    'H': pad(hours),
                    // Hours in 24h format, 0 through 23
                    'k': hours,
                    // Two digits hours in 12h format, 00 through 11
                    'I': pad((hours % 12) || 12),
                    // Hours in 12h format, 1 through 12
                    'l': (hours % 12) || 12,
                    // Two digits minutes, 00 through 59
                    'M': pad(date[D.hcGetMinutes]()),
                    // Upper case AM or PM
                    'p': hours < 12 ? 'AM' : 'PM',
                    // Lower case AM or PM
                    'P': hours < 12 ? 'am' : 'pm',
                    // Two digits seconds, 00 through  59
                    'S': pad(date.getSeconds()),
                    // Milliseconds (naming from Ruby)
                    'L': pad(Math.round(timestamp % 1000), 3)
                }, H.dateFormats);


            // Do the replaces
            for (key in replacements) {
                // Regex would do it in one line, but this is faster
                while (format.indexOf('%' + key) !== -1) {
                    format = format.replace(
                        '%' + key,
                        typeof replacements[key] === 'function' ?
                        replacements[key](timestamp) :
                        replacements[key]
                    );
                }
            }

            // Optionally capitalize the string and return
            return capitalize ?
                format.substr(0, 1).toUpperCase() + format.substr(1) :
                format;
        };

        /**
         * Format a single variable. Similar to sprintf, without the % prefix.
         *
         * @example
         * formatSingle('.2f', 5); // => '5.00'.
         *
         * @function #formatSingle
         * @memberOf Highcharts
         * @param {String} format The format string.
         * @param {*} val The value.
         * @returns {String} The formatted representation of the value.
         */
        H.formatSingle = function(format, val) {
            var floatRegex = /f$/,
                decRegex = /\.([0-9])/,
                lang = H.defaultOptions.lang,
                decimals;

            if (floatRegex.test(format)) { // float
                decimals = format.match(decRegex);
                decimals = decimals ? decimals[1] : -1;
                if (val !== null) {
                    val = H.numberFormat(
                        val,
                        decimals,
                        lang.decimalPoint,
                        format.indexOf(',') > -1 ? lang.thousandsSep : ''
                    );
                }
            } else {
                val = H.dateFormat(format, val);
            }
            return val;
        };

        /**
         * Format a string according to a subset of the rules of Python's String.format
         * method.
         *
         * @function #format
         * @memberOf Highcharts
         * @param {String} str The string to format.
         * @param {Object} ctx The context, a collection of key-value pairs where each
         *        key is replaced by its value.
         * @returns {String} The formatted string.
         *
         * @example
         * var s = Highcharts.format(
         *     'The {color} fox was {len:.2f} feet long',
         *     { color: 'red', len: Math.PI }
         * );
         * // => The red fox was 3.14 feet long
         */
        H.format = function(str, ctx) {
            var splitter = '{',
                isInside = false,
                segment,
                valueAndFormat,
                path,
                i,
                len,
                ret = [],
                val,
                index;

            while (str) {
                index = str.indexOf(splitter);
                if (index === -1) {
                    break;
                }

                segment = str.slice(0, index);
                if (isInside) { // we're on the closing bracket looking back

                    valueAndFormat = segment.split(':');
                    path = valueAndFormat.shift().split('.'); // get first and leave
                    len = path.length;
                    val = ctx;

                    // Assign deeper paths
                    for (i = 0; i < len; i++) {
                        val = val[path[i]];
                    }

                    // Format the replacement
                    if (valueAndFormat.length) {
                        val = H.formatSingle(valueAndFormat.join(':'), val);
                    }

                    // Push the result and advance the cursor
                    ret.push(val);

                } else {
                    ret.push(segment);

                }
                str = str.slice(index + 1); // the rest
                isInside = !isInside; // toggle
                splitter = isInside ? '}' : '{'; // now look for next matching bracket
            }
            ret.push(str);
            return ret.join('');
        };

        /**
         * Get the magnitude of a number.
         *
         * @function #getMagnitude
         * @memberOf Highcharts
         * @param {Number} number The number.
         * @returns {Number} The magnitude, where 1-9 are magnitude 1, 10-99 magnitude 2
         *        etc.
         */
        H.getMagnitude = function(num) {
            return Math.pow(10, Math.floor(Math.log(num) / Math.LN10));
        };

        /**
         * Take an interval and normalize it to multiples of round numbers.
         *
         * @todo  Move this function to the Axis prototype. It is here only for
         *        historical reasons.
         * @function #normalizeTickInterval
         * @memberOf Highcharts
         * @param {Number} interval - The raw, un-rounded interval.
         * @param {Array} [multiples] - Allowed multiples.
         * @param {Number} [magnitude] - The magnitude of the number.
         * @param {Boolean} [allowDecimals] - Whether to allow decimals.
         * @param {Boolean} [hasTickAmount] - If it has tickAmount, avoid landing
         *        on tick intervals lower than original.
         * @returns {Number} The normalized interval.
         */
        H.normalizeTickInterval = function(interval, multiples, magnitude,
            allowDecimals, hasTickAmount) {
            var normalized,
                i,
                retInterval = interval;

            // round to a tenfold of 1, 2, 2.5 or 5
            magnitude = H.pick(magnitude, 1);
            normalized = interval / magnitude;

            // multiples for a linear scale
            if (!multiples) {
                multiples = hasTickAmount ?
                    // Finer grained ticks when the tick amount is hard set, including
                    // when alignTicks is true on multiple axes (#4580).
                    [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10] :

                    // Else, let ticks fall on rounder numbers
                    [1, 2, 2.5, 5, 10];


                // the allowDecimals option
                if (allowDecimals === false) {
                    if (magnitude === 1) {
                        multiples = H.grep(multiples, function(num) {
                            return num % 1 === 0;
                        });
                    } else if (magnitude <= 0.1) {
                        multiples = [1 / magnitude];
                    }
                }
            }

            // normalize the interval to the nearest multiple
            for (i = 0; i < multiples.length; i++) {
                retInterval = multiples[i];
                // only allow tick amounts smaller than natural
                if ((hasTickAmount && retInterval * magnitude >= interval) ||
                    (!hasTickAmount && (normalized <= (multiples[i] +
                        (multiples[i + 1] || multiples[i])) / 2))) {
                    break;
                }
            }

            // multiply back to the correct magnitude
            retInterval *= magnitude;

            return retInterval;
        };


        /**
         * Sort an object array and keep the order of equal items. The ECMAScript
         * standard does not specify the behaviour when items are equal.
         *
         * @function #stableSort
         * @memberOf Highcharts
         * @param {Array} arr - The array to sort.
         * @param {Function} sortFunction - The function to sort it with, like with 
         *        regular Array.prototype.sort.
         * @returns {void}
         */
        H.stableSort = function(arr, sortFunction) {
            var length = arr.length,
                sortValue,
                i;

            // Add index to each item
            for (i = 0; i < length; i++) {
                arr[i].safeI = i; // stable sort index
            }

            arr.sort(function(a, b) {
                sortValue = sortFunction(a, b);
                return sortValue === 0 ? a.safeI - b.safeI : sortValue;
            });

            // Remove index from items
            for (i = 0; i < length; i++) {
                delete arr[i].safeI; // stable sort index
            }
        };

        /**
         * Non-recursive method to find the lowest member of an array. `Math.min` raises
         * a maximum call stack size exceeded error in Chrome when trying to apply more
         * than 150.000 points. This method is slightly slower, but safe.
         *
         * @function #arrayMin
         * @memberOf  Highcharts
         * @param {Array} data An array of numbers.
         * @returns {Number} The lowest number.
         */
        H.arrayMin = function(data) {
            var i = data.length,
                min = data[0];

            while (i--) {
                if (data[i] < min) {
                    min = data[i];
                }
            }
            return min;
        };

        /**
         * Non-recursive method to find the lowest member of an array. `Math.max` raises
         * a maximum call stack size exceeded error in Chrome when trying to apply more
         * than 150.000 points. This method is slightly slower, but safe.
         *
         * @function #arrayMax
         * @memberOf  Highcharts
         * @param {Array} data - An array of numbers.
         * @returns {Number} The highest number.
         */
        H.arrayMax = function(data) {
            var i = data.length,
                max = data[0];

            while (i--) {
                if (data[i] > max) {
                    max = data[i];
                }
            }
            return max;
        };

        /**
         * Utility method that destroys any SVGElement instances that are properties on
         * the given object. It loops all properties and invokes destroy if there is a
         * destroy method. The property is then delete.
         *
         * @function #destroyObjectProperties
         * @memberOf Highcharts
         * @param {Object} obj - The object to destroy properties on.
         * @param {Object} [except] - Exception, do not destroy this property, only
         *    delete it.
         * @returns {void}
         */
        H.destroyObjectProperties = function(obj, except) {
            var n;
            for (n in obj) {
                // If the object is non-null and destroy is defined
                if (obj[n] && obj[n] !== except && obj[n].destroy) {
                    // Invoke the destroy
                    obj[n].destroy();
                }

                // Delete the property from the object.
                delete obj[n];
            }
        };


        /**
         * Discard a HTML element by moving it to the bin and delete.
         *
         * @function #discardElement
         * @memberOf Highcharts
         * @param {HTMLDOMElement} element - The HTML node to discard.
         * @returns {void}
         */
        H.discardElement = function(element) {
            var garbageBin = H.garbageBin;
            // create a garbage bin element, not part of the DOM
            if (!garbageBin) {
                garbageBin = H.createElement('div');
            }

            // move the node and empty bin
            if (element) {
                garbageBin.appendChild(element);
            }
            garbageBin.innerHTML = '';
        };

        /**
         * Fix JS round off float errors.
         *
         * @function #correctFloat
         * @memberOf Highcharts
         * @param {Number} num - A float number to fix.
         * @param {Number} [prec=14] - The precision.
         * @returns {Number} The corrected float number.
         */
        H.correctFloat = function(num, prec) {
            return parseFloat(
                num.toPrecision(prec || 14)
            );
        };

        /**
         * Set the global animation to either a given value, or fall back to the given
         * chart's animation option.
         *
         * @function #setAnimation
         * @memberOf Highcharts
         * @param {Boolean|Animation} animation - The animation object.
         * @param {Object} chart - The chart instance.
         * @returns {void}
         * @todo This function always relates to a chart, and sets a property on the
         *        renderer, so it should be moved to the SVGRenderer.
         */
        H.setAnimation = function(animation, chart) {
            chart.renderer.globalAnimation = H.pick(
                animation,
                chart.options.chart.animation,
                true
            );
        };

        /**
         * Get the animation in object form, where a disabled animation is always
         * returned as `{ duration: 0 }`.
         *
         * @function #animObject
         * @memberOf Highcharts
         * @param {Boolean|AnimationOptions} animation - An animation setting. Can be an
         *        object with duration, complete and easing properties, or a boolean to
         *        enable or disable.
         * @returns {AnimationOptions} An object with at least a duration property.
         */
        H.animObject = function(animation) {
            return H.isObject(animation) ?
                H.merge(animation) : {
                    duration: animation ? 500 : 0
                };
        };

        /**
         * The time unit lookup
         */
        H.timeUnits = {
            millisecond: 1,
            second: 1000,
            minute: 60000,
            hour: 3600000,
            day: 24 * 3600000,
            week: 7 * 24 * 3600000,
            month: 28 * 24 * 3600000,
            year: 364 * 24 * 3600000
        };

        /**
         * Format a number and return a string based on input settings.
         *
         * @function #numberFormat
         * @memberOf Highcharts
         * @param {Number} number - The input number to format.
         * @param {Number} decimals - The amount of decimals.
         * @param {String} [decimalPoint] - The decimal point, defaults to the one given
         *        in the lang options.
         * @param {String} [thousandsSep] - The thousands separator, defaults to the one
         *        given in the lang options.
         * @returns {String} The formatted number.
         */
        H.numberFormat = function(number, decimals, decimalPoint, thousandsSep) {

            number = +number || 0;
            decimals = +decimals;

            var lang = H.defaultOptions.lang,
                origDec = (number.toString().split('.')[1] || '').length,
                decimalComponent,
                strinteger,
                thousands,
                absNumber = Math.abs(number),
                ret;

            if (decimals === -1) {
                // Preserve decimals. Not huge numbers (#3793).
                decimals = Math.min(origDec, 20);
            } else if (!H.isNumber(decimals)) {
                decimals = 2;
            }

            // A string containing the positive integer component of the number
            strinteger = String(H.pInt(absNumber.toFixed(decimals)));

            // Leftover after grouping into thousands. Can be 0, 1 or 3.
            thousands = strinteger.length > 3 ? strinteger.length % 3 : 0;

            // Language
            decimalPoint = H.pick(decimalPoint, lang.decimalPoint);
            thousandsSep = H.pick(thousandsSep, lang.thousandsSep);

            // Start building the return
            ret = number < 0 ? '-' : '';

            // Add the leftover after grouping into thousands. For example, in the
            // number 42 000 000, this line adds 42.
            ret += thousands ? strinteger.substr(0, thousands) + thousandsSep : '';

            // Add the remaining thousands groups, joined by the thousands separator
            ret += strinteger
                .substr(thousands)
                .replace(/(\d{3})(?=\d)/g, '$1' + thousandsSep);

            // Add the decimal point and the decimal component
            if (decimals) {
                // Get the decimal component, and add power to avoid rounding errors
                // with float numbers (#4573)
                decimalComponent = Math.abs(absNumber - strinteger +
                    Math.pow(10, -Math.max(decimals, origDec) - 1));
                ret += decimalPoint + decimalComponent.toFixed(decimals).slice(2);
            }

            return ret;
        };

        /**
         * Easing definition
         * @ignore
         * @param   {Number} pos Current position, ranging from 0 to 1.
         */
        Math.easeInOutSine = function(pos) {
            return -0.5 * (Math.cos(Math.PI * pos) - 1);
        };

        /**
         * Get the computed CSS value for given element and property, only for numerical
         * properties. For width and height, the dimension of the inner box (excluding
         * padding) is returned. Used for fitting the chart within the container.
         *
         * @function #getStyle
         * @memberOf Highcharts
         * @param {HTMLDOMElement} el - A HTML element.
         * @param {String} prop - The property name.
         * @returns {Number} - The numeric value.
         */
        H.getStyle = function(el, prop) {

            var style;

            // For width and height, return the actual inner pixel size (#4913)
            if (prop === 'width') {
                return Math.min(el.offsetWidth, el.scrollWidth) -
                    H.getStyle(el, 'padding-left') -
                    H.getStyle(el, 'padding-right');
            } else if (prop === 'height') {
                return Math.min(el.offsetHeight, el.scrollHeight) -
                    H.getStyle(el, 'padding-top') -
                    H.getStyle(el, 'padding-bottom');
            }

            // Otherwise, get the computed style
            style = win.getComputedStyle(el, undefined);
            return style && H.pInt(style.getPropertyValue(prop));
        };

        /**
         * Search for an item in an array.
         *
         * @function #inArray
         * @memberOf Highcharts
         * @param {*} item - The item to search for.
         * @param {arr} arr - The array or node collection to search in.
         * @returns {Number} - The index within the array, or -1 if not found.
         */
        H.inArray = function(item, arr) {
            return arr.indexOf ? arr.indexOf(item) : [].indexOf.call(arr, item);
        };

        /**
         * Filter an array by a callback.
         *
         * @function #grep
         * @memberOf Highcharts
         * @param {Array} arr - The array to filter.
         * @param {Function} callback - The callback function. The function receives the
         *        item as the first argument. Return `true` if the item is to be
         *        preserved.
         * @returns {Array} - A new, filtered array.
         */
        H.grep = function(arr, callback) {
            return [].filter.call(arr, callback);
        };

        /**
         * Map an array by a callback.
         *
         * @function #map
         * @memberOf Highcharts
         * @param {Array} arr - The array to map.
         * @param {Function} fn - The callback function. Return the new value for the 
         *        new array.
         * @returns {Array} - A new array item with modified items.
         */
        H.map = function(arr, fn) {
            var results = [],
                i = 0,
                len = arr.length;

            for (; i < len; i++) {
                results[i] = fn.call(arr[i], arr[i], i, arr);
            }

            return results;
        };

        /**
         * Get the element's offset position, corrected for `overflow: auto`.
         *
         * @function #offset
         * @memberOf Highcharts
         * @param {HTMLDOMElement} el - The HTML element.
         * @returns {Object} An object containing `left` and `top` properties for the
         * position in the page.
         */
        H.offset = function(el) {
            var docElem = doc.documentElement,
                box = el.getBoundingClientRect();

            return {
                top: box.top + (win.pageYOffset || docElem.scrollTop) -
                    (docElem.clientTop || 0),
                left: box.left + (win.pageXOffset || docElem.scrollLeft) -
                    (docElem.clientLeft || 0)
            };
        };

        /**
         * Stop running animation.
         *
         * @todo A possible extension to this would be to stop a single property, when
         * we want to continue animating others. Then assign the prop to the timer
         * in the Fx.run method, and check for the prop here. This would be an
         * improvement in all cases where we stop the animation from .attr. Instead of
         * stopping everything, we can just stop the actual attributes we're setting.
         *
         * @function #stop
         * @memberOf Highcharts
         * @param {SVGElement} el - The SVGElement to stop animation on.
         * @param {string} [prop] - The property to stop animating. If given, the stop
         *    method will stop a single property from animating, while others continue.
         * @returns {void}
         */
        H.stop = function(el, prop) {

            var i = timers.length;

            // Remove timers related to this element (#4519)
            while (i--) {
                if (timers[i].elem === el && (!prop || prop === timers[i].prop)) {
                    timers[i].stopped = true; // #4667
                }
            }
        };

        /**
         * Iterate over an array.
         *
         * @function #each
         * @memberOf Highcharts
         * @param {Array} arr - The array to iterate over.
         * @param {Function} fn - The iterator callback. It passes two arguments:
         * * item - The array item.
         * * index - The item's index in the array.
         * @param {Object} [ctx] The context.
         */
        H.each = function(arr, fn, ctx) { // modern browsers
            return Array.prototype.forEach.call(arr, fn, ctx);
        };

        /**
         * Add an event listener.
         *
         * @function #addEvent
         * @memberOf Highcharts
         * @param {Object} el - The element or object to add a listener to. It can be a
         *        {@link HTMLDOMElement}, an {@link SVGElement} or any other object.
         * @param {String} type - The event type.
         * @param {Function} fn - The function callback to execute when the event is 
         *        fired.
         * @returns {Function} A callback function to remove the added event.
         */
        H.addEvent = function(el, type, fn) {

            var events = el.hcEvents = el.hcEvents || {};

            function wrappedFn(e) {
                e.target = e.srcElement || win; // #2820
                fn.call(el, e);
            }

            // Handle DOM events in modern browsers
            if (el.addEventListener) {
                el.addEventListener(type, fn, false);

                // Handle old IE implementation
            } else if (el.attachEvent) {

                if (!el.hcEventsIE) {
                    el.hcEventsIE = {};
                }

                // Link wrapped fn with original fn, so we can get this in removeEvent
                el.hcEventsIE[fn.toString()] = wrappedFn;

                el.attachEvent('on' + type, wrappedFn);
            }

            if (!events[type]) {
                events[type] = [];
            }

            events[type].push(fn);

            // Return a function that can be called to remove this event.
            return function() {
                H.removeEvent(el, type, fn);
            };
        };

        /**
         * Remove an event that was added with {@link Highcharts#addEvent}.
         *
         * @function #removeEvent
         * @memberOf Highcharts
         * @param {Object} el - The element to remove events on.
         * @param {String} [type] - The type of events to remove. If undefined, all
         *        events are removed from the element.
         * @param {Function} [fn] - The specific callback to remove. If undefined, all
         *        events that match the element and optionally the type are removed.
         * @returns {void}
         */
        H.removeEvent = function(el, type, fn) {

            var events,
                hcEvents = el.hcEvents,
                index;

            function removeOneEvent(type, fn) {
                if (el.removeEventListener) {
                    el.removeEventListener(type, fn, false);
                } else if (el.attachEvent) {
                    fn = el.hcEventsIE[fn.toString()];
                    el.detachEvent('on' + type, fn);
                }
            }

            function removeAllEvents() {
                var types,
                    len,
                    n;

                if (!el.nodeName) {
                    return; // break on non-DOM events
                }

                if (type) {
                    types = {};
                    types[type] = true;
                } else {
                    types = hcEvents;
                }

                for (n in types) {
                    if (hcEvents[n]) {
                        len = hcEvents[n].length;
                        while (len--) {
                            removeOneEvent(n, hcEvents[n][len]);
                        }
                    }
                }
            }

            if (hcEvents) {
                if (type) {
                    events = hcEvents[type] || [];
                    if (fn) {
                        index = H.inArray(fn, events);
                        if (index > -1) {
                            events.splice(index, 1);
                            hcEvents[type] = events;
                        }
                        removeOneEvent(type, fn);

                    } else {
                        removeAllEvents();
                        hcEvents[type] = [];
                    }
                } else {
                    removeAllEvents();
                    el.hcEvents = {};
                }
            }
        };

        /**
         * Fire an event that was registered with {@link Highcharts#addEvent}.
         *
         * @function #fireEvent
         * @memberOf Highcharts
         * @param {Object} el - The object to fire the event on. It can be a
         *        {@link HTMLDOMElement}, an {@link SVGElement} or any other object.
         * @param {String} type - The type of event.
         * @param {Object} [eventArguments] - Custom event arguments that are passed on
         *        as an argument to the event handler.
         * @param {Function} [defaultFunction] - The default function to execute if the 
         *        other listeners haven't returned false.
         * @returns {void}
         */
        H.fireEvent = function(el, type, eventArguments, defaultFunction) {
            var e,
                hcEvents = el.hcEvents,
                events,
                len,
                i,
                fn;

            eventArguments = eventArguments || {};

            if (doc.createEvent && (el.dispatchEvent || el.fireEvent)) {
                e = doc.createEvent('Events');
                e.initEvent(type, true, true);
                //e.target = el;

                H.extend(e, eventArguments);

                if (el.dispatchEvent) {
                    el.dispatchEvent(e);
                } else {
                    el.fireEvent(type, e);
                }

            } else if (hcEvents) {

                events = hcEvents[type] || [];
                len = events.length;

                if (!eventArguments.target) { // We're running a custom event

                    H.extend(eventArguments, {
                        // Attach a simple preventDefault function to skip default
                        // handler if called. The built-in defaultPrevented property is
                        // not overwritable (#5112)
                        preventDefault: function() {
                            eventArguments.defaultPrevented = true;
                        },
                        // Setting target to native events fails with clicking the
                        // zoom-out button in Chrome.
                        target: el,
                        // If the type is not set, we're running a custom event (#2297).
                        // If it is set, we're running a browser event, and setting it
                        // will cause en error in IE8 (#2465).		
                        type: type
                    });
                }


                for (i = 0; i < len; i++) {
                    fn = events[i];

                    // If the event handler return false, prevent the default handler
                    // from executing
                    if (fn && fn.call(el, eventArguments) === false) {
                        eventArguments.preventDefault();
                    }
                }
            }

            // Run the default if not prevented
            if (defaultFunction && !eventArguments.defaultPrevented) {
                defaultFunction(eventArguments);
            }
        };

        /**
         * An animation configuration. Animation configurations can also be defined as
         * booleans, where `false` turns off animation and `true` defaults to a duration
         * of 500ms.
         * @typedef {Object} AnimationOptions
         * @property {Number} duration - The animation duration in milliseconds.
         * @property {String} [easing] - The name of an easing function as defined on
         *     the `Math` object.
         * @property {Function} [complete] - A callback function to exectute when the
         *     animation finishes.
         * @property {Function} [step] - A callback function to execute on each step of
         *     each attribute or CSS property that's being animated. The first argument
         *     contains information about the animation and progress.
         */


        /**
         * The global animate method, which uses Fx to create individual animators.
         *
         * @function #animate
         * @memberOf Highcharts
         * @param {HTMLDOMElement|SVGElement} el - The element to animate.
         * @param {Object} params - An object containing key-value pairs of the
         *        properties to animate. Supports numeric as pixel-based CSS properties
         *        for HTML objects and attributes for SVGElements.
         * @param {AnimationOptions} [opt] - Animation options.
         */
        H.animate = function(el, params, opt) {
            var start,
                unit = '',
                end,
                fx,
                args,
                prop;

            if (!H.isObject(opt)) { // Number or undefined/null
                args = arguments;
                opt = {
                    duration: args[2],
                    easing: args[3],
                    complete: args[4]
                };
            }
            if (!H.isNumber(opt.duration)) {
                opt.duration = 400;
            }
            opt.easing = typeof opt.easing === 'function' ?
                opt.easing :
                (Math[opt.easing] || Math.easeInOutSine);
            opt.curAnim = H.merge(params);

            for (prop in params) {

                // Stop current running animation of this property
                H.stop(el, prop);

                fx = new H.Fx(el, opt, prop);
                end = null;

                if (prop === 'd') {
                    fx.paths = fx.initPath(
                        el,
                        el.d,
                        params.d
                    );
                    fx.toD = params.d;
                    start = 0;
                    end = 1;
                } else if (el.attr) {
                    start = el.attr(prop);
                } else {
                    start = parseFloat(H.getStyle(el, prop)) || 0;
                    if (prop !== 'opacity') {
                        unit = 'px';
                    }
                }

                if (!end) {
                    end = params[prop];
                }
                if (end.match && end.match('px')) {
                    end = end.replace(/px/g, ''); // #4351
                }
                fx.run(start, end, unit);
            }
        };

        /**
         * Factory to create new series prototypes.
         *
         * @function #seriesType
         * @memberOf Highcharts
         *
         * @param {String} type - The series type name.
         * @param {String} parent - The parent series type name. Use `line` to inherit
         *        from the basic {@link Series} object.
         * @param {Object} options - The additional default options that is merged with
         *        the parent's options.
         * @param {Object} props - The properties (functions and primitives) to set on
         *        the new prototype.
         * @param {Object} [pointProps] - Members for a series-specific extension of the
         *        {@link Point} prototype if needed.
         * @returns {*} - The newly created prototype as extended from {@link Series}
         * or its derivatives.
         */
        // docs: add to API + extending Highcharts
        H.seriesType = function(type, parent, options, props, pointProps) {
            var defaultOptions = H.getOptions(),
                seriesTypes = H.seriesTypes;

            // Merge the options
            defaultOptions.plotOptions[type] = H.merge(
                defaultOptions.plotOptions[parent],
                options
            );

            // Create the class
            seriesTypes[type] = H.extendClass(seriesTypes[parent] ||
                function() {}, props);
            seriesTypes[type].prototype.type = type;

            // Create the point class if needed
            if (pointProps) {
                seriesTypes[type].prototype.pointClass =
                    H.extendClass(H.Point, pointProps);
            }

            return seriesTypes[type];
        };

        /**
         * Get a unique key for using in internal element id's and pointers. The key
         * is composed of a random hash specific to this Highcharts instance, and a 
         * counter.
         * @function #uniqueKey
         * @memberOf Highcharts
         * @return {string} The key.
         * @example
         * var id = H.uniqueKey(); // => 'highcharts-x45f6hp-0'
         */
        H.uniqueKey = (function() {

            var uniqueKeyHash = Math.random().toString(36).substring(2, 9),
                idCounter = 0;

            return function() {
                return 'highcharts-' + uniqueKeyHash + '-' + idCounter++;
            };
        }());

        /**
         * Register Highcharts as a plugin in jQuery
         */
        if (win.jQuery) {
            win.jQuery.fn.highcharts = function() {
                var args = [].slice.call(arguments);

                if (this[0]) { // this[0] is the renderTo div

                    // Create the chart
                    if (args[0]) {
                        new H[ // eslint-disable-line no-new
                            // Constructor defaults to Chart
                            H.isString(args[0]) ? args.shift() : 'Chart'
                        ](this[0], args[0], args[1]);
                        return this;
                    }

                    // When called without parameters or with the return argument,
                    // return an existing chart
                    return charts[H.attr(this[0], 'data-highcharts-chart')];
                }
            };
        }


        /**
         * Compatibility section to add support for legacy IE. This can be removed if
         * old IE support is not needed.
         */
        if (doc && !doc.defaultView) {
            H.getStyle = function(el, prop) {
                var val,
                    alias = {
                        width: 'clientWidth',
                        height: 'clientHeight'
                    }[prop];

                if (el.style[prop]) {
                    return H.pInt(el.style[prop]);
                }
                if (prop === 'opacity') {
                    prop = 'filter';
                }

                // Getting the rendered width and height
                if (alias) {
                    el.style.zoom = 1;
                    return Math.max(el[alias] - 2 * H.getStyle(el, 'padding'), 0);
                }

                val = el.currentStyle[prop.replace(/\-(\w)/g, function(a, b) {
                    return b.toUpperCase();
                })];
                if (prop === 'filter') {
                    val = val.replace(
                        /alpha\(opacity=([0-9]+)\)/,
                        function(a, b) {
                            return b / 100;
                        }
                    );
                }

                return val === '' ? 1 : H.pInt(val);
            };
        }

        if (!Array.prototype.forEach) {
            H.each = function(arr, fn, ctx) { // legacy
                var i = 0,
                    len = arr.length;
                for (; i < len; i++) {
                    if (fn.call(ctx, arr[i], i, arr) === false) {
                        return i;
                    }
                }
            };
        }

        if (!Array.prototype.indexOf) {
            H.inArray = function(item, arr) {
                var len,
                    i = 0;

                if (arr) {
                    len = arr.length;

                    for (; i < len; i++) {
                        if (arr[i] === item) {
                            return i;
                        }
                    }
                }

                return -1;
            };
        }

        if (!Array.prototype.filter) {
            H.grep = function(elements, fn) {
                var ret = [],
                    i = 0,
                    length = elements.length;

                for (; i < length; i++) {
                    if (fn(elements[i], i)) {
                        ret.push(elements[i]);
                    }
                }

                return ret;
            };
        }

        //--- End compatibility section ---

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var each = H.each,
            isNumber = H.isNumber,
            map = H.map,
            merge = H.merge,
            pInt = H.pInt;

        /**
         * @typedef {string} ColorString
         * A valid color to be parsed and handled by Highcharts. Highcharts internally 
         * supports hex colors like `#ffffff`, rgb colors like `rgb(255,255,255)` and
         * rgba colors like `rgba(255,255,255,1)`. Other colors may be supported by the
         * browsers and displayed correctly, but Highcharts is not able to process them
         * and apply concepts like opacity and brightening.
         */
        /**
         * Handle color operations. The object methods are chainable.
         * @param {String} input The input color in either rbga or hex format
         */
        H.Color = function(input) {
            // Backwards compatibility, allow instanciation without new
            if (!(this instanceof H.Color)) {
                return new H.Color(input);
            }
            // Initialize
            this.init(input);
        };
        H.Color.prototype = {

            // Collection of parsers. This can be extended from the outside by pushing parsers
            // to Highcharts.Color.prototype.parsers.
            parsers: [{
                // RGBA color
                regex: /rgba\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]?(?:\.[0-9]+)?)\s*\)/,
                parse: function(result) {
                    return [pInt(result[1]), pInt(result[2]), pInt(result[3]), parseFloat(result[4], 10)];
                }
            }, {
                // HEX color
                regex: /#([a-fA-F0-9]{2})([a-fA-F0-9]{2})([a-fA-F0-9]{2})/,
                parse: function(result) {
                    return [pInt(result[1], 16), pInt(result[2], 16), pInt(result[3], 16), 1];
                }
            }, {
                // RGB color
                regex: /rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)/,
                parse: function(result) {
                    return [pInt(result[1]), pInt(result[2]), pInt(result[3]), 1];
                }
            }],

            // Collection of named colors. Can be extended from the outside by adding colors
            // to Highcharts.Color.prototype.names.
            names: {
                white: '#ffffff',
                black: '#000000'
            },

            /**
             * Parse the input color to rgba array
             * @param {String} input
             */
            init: function(input) {
                var result,
                    rgba,
                    i,
                    parser;

                this.input = input = this.names[input] || input;

                // Gradients
                if (input && input.stops) {
                    this.stops = map(input.stops, function(stop) {
                        return new H.Color(stop[1]);
                    });

                    // Solid colors
                } else {
                    i = this.parsers.length;
                    while (i-- && !rgba) {
                        parser = this.parsers[i];
                        result = parser.regex.exec(input);
                        if (result) {
                            rgba = parser.parse(result);
                        }
                    }
                }
                this.rgba = rgba || [];
            },

            /**
             * Return the color a specified format
             * @param {String} format
             */
            get: function(format) {
                var input = this.input,
                    rgba = this.rgba,
                    ret;

                if (this.stops) {
                    ret = merge(input);
                    ret.stops = [].concat(ret.stops);
                    each(this.stops, function(stop, i) {
                        ret.stops[i] = [ret.stops[i][0], stop.get(format)];
                    });

                    // it's NaN if gradient colors on a column chart
                } else if (rgba && isNumber(rgba[0])) {
                    if (format === 'rgb' || (!format && rgba[3] === 1)) {
                        ret = 'rgb(' + rgba[0] + ',' + rgba[1] + ',' + rgba[2] + ')';
                    } else if (format === 'a') {
                        ret = rgba[3];
                    } else {
                        ret = 'rgba(' + rgba.join(',') + ')';
                    }
                } else {
                    ret = input;
                }
                return ret;
            },

            /**
             * Brighten the color
             * @param {Number} alpha
             */
            brighten: function(alpha) {
                var i,
                    rgba = this.rgba;

                if (this.stops) {
                    each(this.stops, function(stop) {
                        stop.brighten(alpha);
                    });

                } else if (isNumber(alpha) && alpha !== 0) {
                    for (i = 0; i < 3; i++) {
                        rgba[i] += pInt(alpha * 255);

                        if (rgba[i] < 0) {
                            rgba[i] = 0;
                        }
                        if (rgba[i] > 255) {
                            rgba[i] = 255;
                        }
                    }
                }
                return this;
            },

            /**
             * Set the color's opacity to a given alpha value
             * @param {Number} alpha
             */
            setOpacity: function(alpha) {
                this.rgba[3] = alpha;
                return this;
            }
        };
        H.color = function(input) {
            return new H.Color(input);
        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var SVGElement,
            SVGRenderer,

            addEvent = H.addEvent,
            animate = H.animate,
            attr = H.attr,
            charts = H.charts,
            color = H.color,
            css = H.css,
            createElement = H.createElement,
            defined = H.defined,
            deg2rad = H.deg2rad,
            destroyObjectProperties = H.destroyObjectProperties,
            doc = H.doc,
            each = H.each,
            extend = H.extend,
            erase = H.erase,
            grep = H.grep,
            hasTouch = H.hasTouch,
            isArray = H.isArray,
            isFirefox = H.isFirefox,
            isMS = H.isMS,
            isObject = H.isObject,
            isString = H.isString,
            isWebKit = H.isWebKit,
            merge = H.merge,
            noop = H.noop,
            pick = H.pick,
            pInt = H.pInt,
            removeEvent = H.removeEvent,
            splat = H.splat,
            stop = H.stop,
            svg = H.svg,
            SVG_NS = H.SVG_NS,
            symbolSizes = H.symbolSizes,
            win = H.win;

        /**
         * @typedef {Object} SVGDOMElement - An SVG DOM element.
         */
        /**
         * The SVGElement prototype is a JavaScript wrapper for SVG elements used in the
         * rendering layer of Highcharts. Combined with the {@link SVGRenderer} object,
         * these prototypes allow freeform annotation in the charts or even in HTML
         * pages without instanciating a chart. The SVGElement can also wrap HTML
         * labels, when `text` or `label` elements are created with the `useHTML`
         * parameter.
         *
         * The SVGElement instances are created through factory functions on the 
         * {@link SVGRenderer} object, like [rect]{@link SVGRenderer#rect},
         * [path]{@link SVGRenderer#path}, [text]{@link SVGRenderer#text}, [label]{@link
         * SVGRenderer#label}, [g]{@link SVGRenderer#g} and more.
         *
         * @class
         */
        SVGElement = H.SVGElement = function() {
            return this;
        };
        SVGElement.prototype = {

            // Default base for animation
            opacity: 1,
            SVG_NS: SVG_NS,

            /**
             * For labels, these CSS properties are applied to the `text` node directly.
             * @type {Array.<string>}
             */
            textProps: ['direction', 'fontSize', 'fontWeight', 'fontFamily',
                'fontStyle', 'color', 'lineHeight', 'width', 'textDecoration',
                'textOverflow', 'textOutline'
            ],

            /**
             * Initialize the SVG renderer. This function only exists to make the
             * initiation process overridable. It should not be called directly.
             *
             * @param {SVGRenderer} renderer The SVGRenderer instance to initialize to.
             * @param {String} nodeName The SVG node name.
             * @returns {void}
             */
            init: function(renderer, nodeName) {

                /** 
                 * The DOM node. Each SVGRenderer instance wraps a main DOM node, but 
                 * may also represent more nodes.
                 * @type {SVGDOMNode|HTMLDOMNode}
                 */
                this.element = nodeName === 'span' ?
                    createElement(nodeName) :
                    doc.createElementNS(this.SVG_NS, nodeName);

                /**
                 * The renderer that the SVGElement belongs to.
                 * @type {SVGRenderer}
                 */
                this.renderer = renderer;
            },

            /**
             * Animate to given attributes or CSS properties.
             * 
             * @param {SVGAttributes} params SVG attributes or CSS to animate.
             * @param {AnimationOptions} [options] Animation options.
             * @param {Function} [complete] Function to perform at the end of animation.
             * @returns {SVGElement} Returns the SVGElement for chaining.
             */
            animate: function(params, options, complete) {
                var animOptions = pick(options, this.renderer.globalAnimation, true);
                if (animOptions) {
                    if (complete) { // allows using a callback with the global animation without overwriting it
                        animOptions.complete = complete;
                    }
                    animate(this, params, animOptions);
                } else {
                    this.attr(params, null, complete);
                }
                return this;
            },

            /**
             * @typedef {Object} GradientOptions
             * @property {Object} linearGradient Holds an object that defines the start
             *    position and the end position relative to the shape.
             * @property {Number} linearGradient.x1 Start horizontal position of the
             *    gradient. Ranges 0-1.
             * @property {Number} linearGradient.x2 End horizontal position of the
             *    gradient. Ranges 0-1.
             * @property {Number} linearGradient.y1 Start vertical position of the
             *    gradient. Ranges 0-1.
             * @property {Number} linearGradient.y2 End vertical position of the
             *    gradient. Ranges 0-1.
             * @property {Object} radialGradient Holds an object that defines the center
             *    position and the radius.
             * @property {Number} radialGradient.cx Center horizontal position relative
             *    to the shape. Ranges 0-1.
             * @property {Number} radialGradient.cy Center vertical position relative
             *    to the shape. Ranges 0-1.
             * @property {Number} radialGradient.r Radius relative to the shape. Ranges
             *    0-1.
             * @property {Array.<Array>} stops The first item in each tuple is the
             *    position in the gradient, where 0 is the start of the gradient and 1
             *    is the end of the gradient. Multiple stops can be applied. The second
             *    item is the color for each stop. This color can also be given in the
             *    rgba format.
             *
             * @example
             * // Linear gradient used as a color option
             * color: {
             *     linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
             *         stops: [
             *             [0, '#003399'], // start
             *             [0.5, '#ffffff'], // middle
             *             [1, '#3366AA'] // end
             *         ]
             *     }
             * }
             */
            /**
             * Build and apply an SVG gradient out of a common JavaScript configuration
             * object. This function is called from the attribute setters.
             *
             * @private
             * @param {GradientOptions} color The gradient options structure.
             * @param {string} prop The property to apply, can either be `fill` or
             * `stroke`. 
             * @param {SVGDOMElement} elem SVG DOM element to apply the gradient on.
             */
            colorGradient: function(color, prop, elem) {
                var renderer = this.renderer,
                    colorObject,
                    gradName,
                    gradAttr,
                    radAttr,
                    gradients,
                    gradientObject,
                    stops,
                    stopColor,
                    stopOpacity,
                    radialReference,
                    n,
                    id,
                    key = [],
                    value;

                // Apply linear or radial gradients
                if (color.linearGradient) {
                    gradName = 'linearGradient';
                } else if (color.radialGradient) {
                    gradName = 'radialGradient';
                }

                if (gradName) {
                    gradAttr = color[gradName];
                    gradients = renderer.gradients;
                    stops = color.stops;
                    radialReference = elem.radialReference;

                    // Keep < 2.2 kompatibility
                    if (isArray(gradAttr)) {
                        color[gradName] = gradAttr = {
                            x1: gradAttr[0],
                            y1: gradAttr[1],
                            x2: gradAttr[2],
                            y2: gradAttr[3],
                            gradientUnits: 'userSpaceOnUse'
                        };
                    }

                    // Correct the radial gradient for the radial reference system
                    if (gradName === 'radialGradient' && radialReference && !defined(gradAttr.gradientUnits)) {
                        radAttr = gradAttr; // Save the radial attributes for updating
                        gradAttr = merge(gradAttr,
                            renderer.getRadialAttr(radialReference, radAttr), {
                                gradientUnits: 'userSpaceOnUse'
                            }
                        );
                    }

                    // Build the unique key to detect whether we need to create a new element (#1282)
                    for (n in gradAttr) {
                        if (n !== 'id') {
                            key.push(n, gradAttr[n]);
                        }
                    }
                    for (n in stops) {
                        key.push(stops[n]);
                    }
                    key = key.join(',');

                    // Check if a gradient object with the same config object is created within this renderer
                    if (gradients[key]) {
                        id = gradients[key].attr('id');

                    } else {

                        // Set the id and create the element
                        gradAttr.id = id = H.uniqueKey();
                        gradients[key] = gradientObject = renderer.createElement(gradName)
                            .attr(gradAttr)
                            .add(renderer.defs);

                        gradientObject.radAttr = radAttr;

                        // The gradient needs to keep a list of stops to be able to destroy them
                        gradientObject.stops = [];
                        each(stops, function(stop) {
                            var stopObject;
                            if (stop[1].indexOf('rgba') === 0) {
                                colorObject = H.color(stop[1]);
                                stopColor = colorObject.get('rgb');
                                stopOpacity = colorObject.get('a');
                            } else {
                                stopColor = stop[1];
                                stopOpacity = 1;
                            }
                            stopObject = renderer.createElement('stop').attr({
                                offset: stop[0],
                                'stop-color': stopColor,
                                'stop-opacity': stopOpacity
                            }).add(gradientObject);

                            // Add the stop element to the gradient
                            gradientObject.stops.push(stopObject);
                        });
                    }

                    // Set the reference to the gradient object
                    value = 'url(' + renderer.url + '#' + id + ')';
                    elem.setAttribute(prop, value);
                    elem.gradient = key;

                    // Allow the color to be concatenated into tooltips formatters etc. (#2995)
                    color.toString = function() {
                        return value;
                    };
                }
            },

            /**
             * Apply a text outline through a custom CSS property, by copying the text
             * element and apply stroke to the copy. Used internally. Contrast checks
             * at http://jsfiddle.net/highcharts/43soe9m1/2/ .
             *
             * @private
             * @param {String} textOutline A custom CSS `text-outline` setting, defined
             *    by `width color`. 
             * @example
             * // Specific color
             * text.css({
             *    textOutline: '1px black'
             * });
             * // Automatic contrast
             * text.css({
             *    color: '#000000', // black text
             *    textOutline: '1px contrast' // => white outline
             * });
             */
            applyTextOutline: function(textOutline) {
                var elem = this.element,
                    tspans,
                    hasContrast = textOutline.indexOf('contrast') !== -1,
                    styles = {},
                    color,
                    strokeWidth;

                // When the text shadow is set to contrast, use dark stroke for light
                // text and vice versa.
                if (hasContrast) {
                    styles.textOutline = textOutline = textOutline.replace(
                        /contrast/g,
                        this.renderer.getContrast(elem.style.fill)
                    );
                }

                this.fakeTS = true; // Fake text shadow

                // In order to get the right y position of the clone,
                // copy over the y setter
                this.ySetter = this.xSetter;

                tspans = [].slice.call(elem.getElementsByTagName('tspan'));

                // Extract the stroke width and color
                textOutline = textOutline.split(' ');
                color = textOutline[textOutline.length - 1];
                strokeWidth = textOutline[0];

                if (strokeWidth && strokeWidth !== 'none') {

                    // Since the stroke is applied on center of the actual outline, we
                    // need to double it to get the correct stroke-width outside the 
                    // glyphs.
                    strokeWidth = strokeWidth.replace(
                        /(^[\d\.]+)(.*?)$/g,
                        function(match, digit, unit) {
                            return (2 * digit) + unit;
                        }
                    );

                    // Remove shadows from previous runs
                    each(tspans, function(tspan) {
                        if (tspan.getAttribute('class') === 'highcharts-text-outline') {
                            // Remove then erase
                            erase(tspans, elem.removeChild(tspan));
                        }
                    });

                    // For each of the tspans, create a stroked copy behind it.
                    each(tspans, function(tspan, y) {
                        var clone;

                        // Let the first line start at the correct X position
                        if (y === 0) {
                            tspan.setAttribute('x', elem.getAttribute('x'));
                            y = elem.getAttribute('y');
                            tspan.setAttribute('y', y || 0);
                            if (y === null) {
                                elem.setAttribute('y', 0);
                            }
                        }

                        // Create the clone and apply outline properties
                        clone = tspan.cloneNode(1);
                        attr(clone, {
                            'class': 'highcharts-text-outline',
                            'fill': color,
                            'stroke': color,
                            'stroke-width': strokeWidth,
                            'stroke-linejoin': 'round'
                        });
                        elem.insertBefore(clone, elem.firstChild);
                    });
                }
            },

            /**
             *
             * @typedef {Object} SVGAttributes An object of key-value pairs for SVG
             *   attributes. Attributes in Highcharts elements for the most parts
             *   correspond to SVG, but some are specific to Highcharts, like `zIndex`,
             *   `rotation`, `translateX`, `translateY`, `scaleX` and `scaleY`. SVG
             *   attributes containing a hyphen are _not_ camel-cased, they should be
             *   quoted to preserve the hyphen.
             * @example
             * {
             *     'stroke': '#ff0000', // basic
             *     'stroke-width': 2, // hyphenated
             *     'rotation': 45 // custom
             *     'd': ['M', 10, 10, 'L', 30, 30, 'z'] // path definition, note format
             * }
             */
            /**
             * Apply native and custom attributes to the SVG elements.
             * 
             * In order to set the rotation center for rotation, set x and y to 0 and
             * use `translateX` and `translateY` attributes to position the element
             * instead.
             *
             * Attributes frequently used in Highcharts are `fill`, `stroke`,
             * `stroke-width`.
             *
             * @param {SVGAttributes|String} hash - The native and custom SVG
             *    attributes. 
             * @param {string} [val] - If the type of the first argument is `string`, 
             *    the second can be a value, which will serve as a single attribute
             *    setter. If the first argument is a string and the second is undefined,
             *    the function serves as a getter and the current value of the property
             *    is returned.
             * @param {Function} complete - A callback function to execute after setting
             *    the attributes. This makes the function compliant and interchangeable
             *    with the {@link SVGElement#animate} function.
             *    
             * @returns {SVGElement|string|number} If used as a setter, it returns the 
             *    current {@link SVGElement} so the calls can be chained. If used as a 
             *    getter, the current value of the attribute is returned.
             * 
             * @example
             * // Set multiple attributes
             * element.attr({
             *     stroke: 'red',
             *     fill: 'blue',
             *     x: 10,
             *     y: 10
             * });
             *
             * // Set a single attribute
             * element.attr('stroke', 'red');
             *
             * // Get an attribute
             * element.attr('stroke'); // => 'red'
             * 
             */
            attr: function(hash, val, complete) {
                var key,
                    value,
                    element = this.element,
                    hasSetSymbolSize,
                    ret = this,
                    skipAttr,
                    setter;

                // single key-value pair
                if (typeof hash === 'string' && val !== undefined) {
                    key = hash;
                    hash = {};
                    hash[key] = val;
                }

                // used as a getter: first argument is a string, second is undefined
                if (typeof hash === 'string') {
                    ret = (this[hash + 'Getter'] || this._defaultGetter).call(this, hash, element);

                    // setter
                } else {

                    for (key in hash) {
                        value = hash[key];
                        skipAttr = false;

                        // Unless .attr is from the animator update, stop current
                        // running animation of this property
                        if (key !== this.animProp) {
                            stop(this, key);
                        }

                        if (this.symbolName && /^(x|y|width|height|r|start|end|innerR|anchorX|anchorY)/.test(key)) {
                            if (!hasSetSymbolSize) {
                                this.symbolAttr(hash);
                                hasSetSymbolSize = true;
                            }
                            skipAttr = true;
                        }

                        if (this.rotation && (key === 'x' || key === 'y')) {
                            this.doTransform = true;
                        }

                        if (!skipAttr) {
                            setter = this[key + 'Setter'] || this._defaultSetter;
                            setter.call(this, value, key, element);


                            // Let the shadow follow the main element
                            if (this.shadows && /^(width|height|visibility|x|y|d|transform|cx|cy|r)$/.test(key)) {
                                this.updateShadows(key, value, setter);
                            }

                        }
                    }

                    // Update transform. Do this outside the loop to prevent redundant updating for batch setting
                    // of attributes.
                    if (this.doTransform) {
                        this.updateTransform();
                        this.doTransform = false;
                    }

                }

                // In accordance with animate, run a complete callback
                if (complete) {
                    complete();
                }

                return ret;
            },


            /**
             * Update the shadow elements with new attributes.
             *
             * @private
             * @param {String} key - The attribute name.
             * @param {String|Number} value - The value of the attribute.
             * @param {Function} setter - The setter function, inherited from the
             *   parent wrapper
             * @returns {void}
             */
            updateShadows: function(key, value, setter) {
                var shadows = this.shadows,
                    i = shadows.length;

                while (i--) {
                    setter.call(
                        shadows[i],
                        key === 'height' ?
                        Math.max(value - (shadows[i].cutHeight || 0), 0) :
                        key === 'd' ? this.d : value,
                        key,
                        shadows[i]
                    );
                }
            },


            /**
             * Add a class name to an element.
             *
             * @param {string} className - The new class name to add.
             * @param {boolean} [replace=false] - When true, the existing class name(s)
             *    will be overwritten with the new one. When false, the new one is
             *    added.
             * @returns {SVGElement} Return the SVG element for chainability.
             */
            addClass: function(className, replace) {
                var currentClassName = this.attr('class') || '';

                if (currentClassName.indexOf(className) === -1) {
                    if (!replace) {
                        className =
                            (currentClassName + (currentClassName ? ' ' : '') +
                                className).replace('  ', ' ');
                    }
                    this.attr('class', className);
                }
                return this;
            },

            /**
             * Check if an element has the given class name.
             * @param  {string}  className - The class name to check for.
             * @return {Boolean}
             */
            hasClass: function(className) {
                return attr(this.element, 'class').indexOf(className) !== -1;
            },

            /**
             * Remove a class name from the element.
             * @param  {string} className The class name to remove.
             * @return {SVGElement} Returns the SVG element for chainability.
             */
            removeClass: function(className) {
                attr(this.element, 'class', (attr(this.element, 'class') || '').replace(className, ''));
                return this;
            },

            /**
             * If one of the symbol size affecting parameters are changed,
             * check all the others only once for each call to an element's
             * .attr() method
             * @param {Object} hash - The attributes to set.
             * @private
             */
            symbolAttr: function(hash) {
                var wrapper = this;

                each(['x', 'y', 'r', 'start', 'end', 'width', 'height', 'innerR', 'anchorX', 'anchorY'], function(key) {
                    wrapper[key] = pick(hash[key], wrapper[key]);
                });

                wrapper.attr({
                    d: wrapper.renderer.symbols[wrapper.symbolName](
                        wrapper.x,
                        wrapper.y,
                        wrapper.width,
                        wrapper.height,
                        wrapper
                    )
                });
            },

            /**
             * Apply a clipping rectangle to this element.
             * 
             * @param {ClipRect} [clipRect] - The clipping rectangle. If skipped, the
             *    current clip is removed.
             * @returns {SVGElement} Returns the SVG element to allow chaining.
             */
            clip: function(clipRect) {
                return this.attr(
                    'clip-path',
                    clipRect ?
                    'url(' + this.renderer.url + '#' + clipRect.id + ')' :
                    'none'
                );
            },

            /**
             * Calculate the coordinates needed for drawing a rectangle crisply and
             * return the calculated attributes.
             * 
             * @param {Object} rect - A rectangle.
             * @param {number} rect.x - The x position.
             * @param {number} rect.y - The y position.
             * @param {number} rect.width - The width.
             * @param {number} rect.height - The height.
             * @param {number} [strokeWidth] - The stroke width to consider when
             *    computing crisp positioning. It can also be set directly on the rect
             *    parameter.
             *
             * @returns {{x: Number, y: Number, width: Number, height: Number}} The
             *    modified rectangle arguments.
             */
            crisp: function(rect, strokeWidth) {

                var wrapper = this,
                    key,
                    attribs = {},
                    normalizer;

                strokeWidth = strokeWidth || rect.strokeWidth || 0;
                normalizer = Math.round(strokeWidth) % 2 / 2; // Math.round because strokeWidth can sometimes have roundoff errors

                // normalize for crisp edges
                rect.x = Math.floor(rect.x || wrapper.x || 0) + normalizer;
                rect.y = Math.floor(rect.y || wrapper.y || 0) + normalizer;
                rect.width = Math.floor((rect.width || wrapper.width || 0) - 2 * normalizer);
                rect.height = Math.floor((rect.height || wrapper.height || 0) - 2 * normalizer);
                if (defined(rect.strokeWidth)) {
                    rect.strokeWidth = strokeWidth;
                }

                for (key in rect) {
                    if (wrapper[key] !== rect[key]) { // only set attribute if changed
                        wrapper[key] = attribs[key] = rect[key];
                    }
                }

                return attribs;
            },

            /**
             * Set styles for the element. In addition to CSS styles supported by 
             * native SVG and HTML elements, there are also some custom made for 
             * Highcharts, like `width`, `ellipsis` and `textOverflow` for SVG text
             * elements.
             * @param {CSSObject} styles The new CSS styles.
             * @returns {SVGElement} Return the SVG element for chaining.
             */
            css: function(styles) {
                var elemWrapper = this,
                    oldStyles = elemWrapper.styles,
                    newStyles = {},
                    elem = elemWrapper.element,
                    textWidth,
                    n,
                    serializedCss = '',
                    hyphenate,
                    hasNew = !oldStyles;

                // convert legacy
                if (styles && styles.color) {
                    styles.fill = styles.color;
                }

                // Filter out existing styles to increase performance (#2640)
                if (oldStyles) {
                    for (n in styles) {
                        if (styles[n] !== oldStyles[n]) {
                            newStyles[n] = styles[n];
                            hasNew = true;
                        }
                    }
                }
                if (hasNew) {
                    textWidth = elemWrapper.textWidth =
                        (styles && styles.width && elem.nodeName.toLowerCase() === 'text' && pInt(styles.width)) ||
                        elemWrapper.textWidth; // #3501

                    // Merge the new styles with the old ones
                    if (oldStyles) {
                        styles = extend(
                            oldStyles,
                            newStyles
                        );
                    }

                    // store object
                    elemWrapper.styles = styles;

                    if (textWidth && (!svg && elemWrapper.renderer.forExport)) {
                        delete styles.width;
                    }

                    // serialize and set style attribute
                    if (isMS && !svg) {
                        css(elemWrapper.element, styles);
                    } else {
                        hyphenate = function(a, b) {
                            return '-' + b.toLowerCase();
                        };
                        for (n in styles) {
                            serializedCss += n.replace(/([A-Z])/g, hyphenate) + ':' + styles[n] + ';';
                        }
                        attr(elem, 'style', serializedCss); // #1881
                    }


                    if (elemWrapper.added) {
                        // Rebuild text after added
                        if (textWidth) {
                            elemWrapper.renderer.buildText(elemWrapper);
                        }

                        // Apply text outline after added
                        if (styles && styles.textOutline) {
                            elemWrapper.applyTextOutline(styles.textOutline);
                        }
                    }
                }

                return elemWrapper;
            },


            /**
             * Get the current stroke width. In classic mode, the setter registers it 
             * directly on the element.
             * @returns {number} The stroke width in pixels.
             * @ignore
             */
            strokeWidth: function() {
                return this['stroke-width'] || 0;
            },


            /**
             * Add an event listener. This is a simple setter that replaces all other
             * events of the same type, opposed to the {@link Highcharts#addEvent}
             * function.
             * @param {string} eventType - The event type. If the type is `click`, 
             *    Highcharts will internally translate it to a `touchstart` event on 
             *    touch devices, to prevent the browser from waiting for a click event
             *    from firing.
             * @param {Function} handler - The handler callback.
             * @returns {SVGElement} The SVGElement for chaining.
             */
            on: function(eventType, handler) {
                var svgElement = this,
                    element = svgElement.element;

                // touch
                if (hasTouch && eventType === 'click') {
                    element.ontouchstart = function(e) {
                        svgElement.touchEventFired = Date.now(); // #2269
                        e.preventDefault();
                        handler.call(element, e);
                    };
                    element.onclick = function(e) {
                        if (win.navigator.userAgent.indexOf('Android') === -1 ||
                            Date.now() - (svgElement.touchEventFired || 0) > 1100) {
                            handler.call(element, e);
                        }
                    };
                } else {
                    // simplest possible event model for internal use
                    element['on' + eventType] = handler;
                }
                return this;
            },

            /**
             * Set the coordinates needed to draw a consistent radial gradient across
             * a shape regardless of positioning inside the chart. Used on pie slices
             * to make all the slices have the same radial reference point.
             *
             * @param {Array} coordinates The center reference. The format is
             *    `[centerX, centerY, diameter]` in pixels.
             * @returns {SVGElement} Returns the SVGElement for chaining.
             */
            setRadialReference: function(coordinates) {
                var existingGradient = this.renderer.gradients[this.element.gradient];

                this.element.radialReference = coordinates;

                // On redrawing objects with an existing gradient, the gradient needs
                // to be repositioned (#3801)
                if (existingGradient && existingGradient.radAttr) {
                    existingGradient.animate(
                        this.renderer.getRadialAttr(
                            coordinates,
                            existingGradient.radAttr
                        )
                    );
                }

                return this;
            },

            /**
             * Move an object and its children by x and y values.
             * 
             * @param {number} x - The x value.
             * @param {number} y - The y value.
             */
            translate: function(x, y) {
                return this.attr({
                    translateX: x,
                    translateY: y
                });
            },

            /**
             * Invert a group, rotate and flip. This is used internally on inverted 
             * charts, where the points and graphs are drawn as if not inverted, then
             * the series group elements are inverted.
             *
             * @param {boolean} inverted - Whether to invert or not. An inverted shape
             *    can be un-inverted by setting it to false.
             * @returns {SVGElement} Return the SVGElement for chaining.
             */
            invert: function(inverted) {
                var wrapper = this;
                wrapper.inverted = inverted;
                wrapper.updateTransform();
                return wrapper;
            },

            /**
             * Update the transform attribute based on internal properties. Deals with
             * the custom `translateX`, `translateY`, `rotation`, `scaleX` and `scaleY`
             * attributes and updates the SVG `transform` attribute.
             * @private
             * @returns {void}
             */
            updateTransform: function() {
                var wrapper = this,
                    translateX = wrapper.translateX || 0,
                    translateY = wrapper.translateY || 0,
                    scaleX = wrapper.scaleX,
                    scaleY = wrapper.scaleY,
                    inverted = wrapper.inverted,
                    rotation = wrapper.rotation,
                    element = wrapper.element,
                    transform;

                // flipping affects translate as adjustment for flipping around the group's axis
                if (inverted) {
                    translateX += wrapper.attr('width');
                    translateY += wrapper.attr('height');
                }

                // Apply translate. Nearly all transformed elements have translation, so instead
                // of checking for translate = 0, do it always (#1767, #1846).
                transform = ['translate(' + translateX + ',' + translateY + ')'];

                // apply rotation
                if (inverted) {
                    transform.push('rotate(90) scale(-1,1)');
                } else if (rotation) { // text rotation
                    transform.push('rotate(' + rotation + ' ' + (element.getAttribute('x') || 0) + ' ' + (element.getAttribute('y') || 0) + ')');

                    // Delete bBox memo when the rotation changes
                    //delete wrapper.bBox;
                }

                // apply scale
                if (defined(scaleX) || defined(scaleY)) {
                    transform.push('scale(' + pick(scaleX, 1) + ' ' + pick(scaleY, 1) + ')');
                }

                if (transform.length) {
                    element.setAttribute('transform', transform.join(' '));
                }
            },

            /**
             * Bring the element to the front.
             *
             * @returns {SVGElement} Returns the SVGElement for chaining.
             */
            toFront: function() {
                var element = this.element;
                element.parentNode.appendChild(element);
                return this;
            },


            /**
             * Align the element relative to the chart or another box.
             * ß
             * @param {Object} [alignOptions] The alignment options. The function can be
             *   called without this parameter in order to re-align an element after the
             *   box has been updated.
             * @param {string} [alignOptions.align=left] Horizontal alignment. Can be
             *   one of `left`, `center` and `right`.
             * @param {string} [alignOptions.verticalAlign=top] Vertical alignment. Can
             *   be one of `top`, `middle` and `bottom`.
             * @param {number} [alignOptions.x=0] Horizontal pixel offset from
             *   alignment.
             * @param {number} [alignOptions.y=0] Vertical pixel offset from alignment.
             * @param {Boolean} [alignByTranslate=false] Use the `transform` attribute
             *   with translateX and translateY custom attributes to align this elements
             *   rather than `x` and `y` attributes.
             * @param {String|Object} box The box to align to, needs a width and height.
             *   When the box is a string, it refers to an object in the Renderer. For
             *   example, when box is `spacingBox`, it refers to `Renderer.spacingBox`
             *   which holds `width`, `height`, `x` and `y` properties.
             * @returns {SVGElement} Returns the SVGElement for chaining.
             */
            align: function(alignOptions, alignByTranslate, box) {
                var align,
                    vAlign,
                    x,
                    y,
                    attribs = {},
                    alignTo,
                    renderer = this.renderer,
                    alignedObjects = renderer.alignedObjects,
                    alignFactor,
                    vAlignFactor;

                // First call on instanciate
                if (alignOptions) {
                    this.alignOptions = alignOptions;
                    this.alignByTranslate = alignByTranslate;
                    if (!box || isString(box)) { // boxes other than renderer handle this internally
                        this.alignTo = alignTo = box || 'renderer';
                        erase(alignedObjects, this); // prevent duplicates, like legendGroup after resize
                        alignedObjects.push(this);
                        box = null; // reassign it below
                    }

                    // When called on resize, no arguments are supplied
                } else {
                    alignOptions = this.alignOptions;
                    alignByTranslate = this.alignByTranslate;
                    alignTo = this.alignTo;
                }

                box = pick(box, renderer[alignTo], renderer);

                // Assign variables
                align = alignOptions.align;
                vAlign = alignOptions.verticalAlign;
                x = (box.x || 0) + (alignOptions.x || 0); // default: left align
                y = (box.y || 0) + (alignOptions.y || 0); // default: top align

                // Align
                if (align === 'right') {
                    alignFactor = 1;
                } else if (align === 'center') {
                    alignFactor = 2;
                }
                if (alignFactor) {
                    x += (box.width - (alignOptions.width || 0)) / alignFactor;
                }
                attribs[alignByTranslate ? 'translateX' : 'x'] = Math.round(x);


                // Vertical align
                if (vAlign === 'bottom') {
                    vAlignFactor = 1;
                } else if (vAlign === 'middle') {
                    vAlignFactor = 2;
                }
                if (vAlignFactor) {
                    y += (box.height - (alignOptions.height || 0)) / vAlignFactor;
                }
                attribs[alignByTranslate ? 'translateY' : 'y'] = Math.round(y);

                // Animate only if already placed
                this[this.placed ? 'animate' : 'attr'](attribs);
                this.placed = true;
                this.alignAttr = attribs;

                return this;
            },

            /**
             * Get the bounding box (width, height, x and y) for the element. Generally
             * used to get rendered text size. Since this is called a lot in charts,
             * the results are cached based on text properties, in order to save DOM
             * traffic. The returned bounding box includes the rotation, so for example
             * a single text line of rotation 90 will report a greater height, and a
             * width corresponding to the line-height.
             *
             * @param {boolean} [reload] Skip the cache and get the updated DOM bouding
             *   box.
             * @param {number} [rot] Override the element's rotation. This is internally
             *   used on axis labels with a value of 0 to find out what the bounding box
             *   would be have been if it were not rotated.
             * @returns {Object} The bounding box with `x`, `y`, `width` and `height`
             * properties.
             */
            getBBox: function(reload, rot) {
                var wrapper = this,
                    bBox, // = wrapper.bBox,
                    renderer = wrapper.renderer,
                    width,
                    height,
                    rotation,
                    rad,
                    element = wrapper.element,
                    styles = wrapper.styles,
                    fontSize,
                    textStr = wrapper.textStr,
                    toggleTextShadowShim,
                    cache = renderer.cache,
                    cacheKeys = renderer.cacheKeys,
                    cacheKey;

                rotation = pick(rot, wrapper.rotation);
                rad = rotation * deg2rad;


                fontSize = styles && styles.fontSize;


                if (textStr !== undefined) {

                    cacheKey = textStr.toString();

                    // Since numbers are monospaced, and numerical labels appear a lot
                    // in a chart, we assume that a label of n characters has the same
                    // bounding box as others of the same length. Unless there is inner
                    // HTML in the label. In that case, leave the numbers as is (#5899).
                    if (cacheKey.indexOf('<') === -1) {
                        cacheKey = cacheKey.replace(/[0-9]/g, '0');
                    }

                    // Properties that affect bounding box
                    cacheKey += [
                            '',
                            rotation || 0,
                            fontSize,
                            element.style.width,
                            element.style['text-overflow'] // #5968
                        ]
                        .join(',');

                }

                if (cacheKey && !reload) {
                    bBox = cache[cacheKey];
                }

                // No cache found
                if (!bBox) {

                    // SVG elements
                    if (element.namespaceURI === wrapper.SVG_NS || renderer.forExport) {
                        try { // Fails in Firefox if the container has display: none.

                            // When the text shadow shim is used, we need to hide the fake shadows
                            // to get the correct bounding box (#3872)
                            toggleTextShadowShim = this.fakeTS && function(display) {
                                each(element.querySelectorAll('.highcharts-text-outline'), function(tspan) {
                                    tspan.style.display = display;
                                });
                            };

                            // Workaround for #3842, Firefox reporting wrong bounding box for shadows
                            if (toggleTextShadowShim) {
                                toggleTextShadowShim('none');
                            }

                            bBox = element.getBBox ?
                                // SVG: use extend because IE9 is not allowed to change width and height in case
                                // of rotation (below)
                                extend({}, element.getBBox()) :
                                // Legacy IE in export mode
                                {
                                    width: element.offsetWidth,
                                    height: element.offsetHeight
                                };

                            // #3842
                            if (toggleTextShadowShim) {
                                toggleTextShadowShim('');
                            }
                        } catch (e) {}

                        // If the bBox is not set, the try-catch block above failed. The other condition
                        // is for Opera that returns a width of -Infinity on hidden elements.
                        if (!bBox || bBox.width < 0) {
                            bBox = {
                                width: 0,
                                height: 0
                            };
                        }


                        // VML Renderer or useHTML within SVG
                    } else {

                        bBox = wrapper.htmlGetBBox();

                    }

                    // True SVG elements as well as HTML elements in modern browsers using the .useHTML option
                    // need to compensated for rotation
                    if (renderer.isSVG) {
                        width = bBox.width;
                        height = bBox.height;

                        // Workaround for wrong bounding box in IE9 and IE10 (#1101, #1505, #1669, #2568)
                        if (isMS && styles && styles.fontSize === '11px' && height.toPrecision(3) === '16.9') {
                            bBox.height = height = 14;
                        }

                        // Adjust for rotated text
                        if (rotation) {
                            bBox.width = Math.abs(height * Math.sin(rad)) + Math.abs(width * Math.cos(rad));
                            bBox.height = Math.abs(height * Math.cos(rad)) + Math.abs(width * Math.sin(rad));
                        }
                    }

                    // Cache it. When loading a chart in a hidden iframe in Firefox and IE/Edge, the
                    // bounding box height is 0, so don't cache it (#5620).
                    if (cacheKey && bBox.height > 0) {

                        // Rotate (#4681)
                        while (cacheKeys.length > 250) {
                            delete cache[cacheKeys.shift()];
                        }

                        if (!cache[cacheKey]) {
                            cacheKeys.push(cacheKey);
                        }
                        cache[cacheKey] = bBox;
                    }
                }
                return bBox;
            },

            /**
             * Show the element after it has been hidden. 
             *
             * @param {boolean} [inherit=false] Set the visibility attribute to
             * `inherit` rather than `visible`. The difference is that an element with
             * `visibility="visible"` will be visible even if the parent is hidden.
             *
             * @returns {SVGElement} Returns the SVGElement for chaining.
             */
            show: function(inherit) {
                return this.attr({
                    visibility: inherit ? 'inherit' : 'visible'
                });
            },

            /**
             * Hide the element, equivalent to setting the `visibility` attribute to
             * `hidden`.
             *
             * @returns {SVGElement} Returns the SVGElement for chaining.
             */
            hide: function() {
                return this.attr({
                    visibility: 'hidden'
                });
            },

            /**
             * Fade out an element by animating its opacity down to 0, and hide it on
             * complete. Used internally for the tooltip.
             * 
             * @param {number} [duration=150] The fade duration in milliseconds.
             */
            fadeOut: function(duration) {
                var elemWrapper = this;
                elemWrapper.animate({
                    opacity: 0
                }, {
                    duration: duration || 150,
                    complete: function() {
                        elemWrapper.attr({
                            y: -9999
                        }); // #3088, assuming we're only using this for tooltips
                    }
                });
            },

            /**
             * Add the element to the DOM. All elements must be added this way.
             * 
             * @param {SVGElement|SVGDOMElement} [parent] The parent item to add it to.
             *   If undefined, the element is added to the {@link SVGRenderer.box}.
             *
             * @returns {SVGElement} Returns the SVGElement for chaining.
             *
             * @sample highcharts/members/renderer-g - Elements added to a group
             */
            add: function(parent) {

                var renderer = this.renderer,
                    element = this.element,
                    inserted;

                if (parent) {
                    this.parentGroup = parent;
                }

                // mark as inverted
                this.parentInverted = parent && parent.inverted;

                // build formatted text
                if (this.textStr !== undefined) {
                    renderer.buildText(this);
                }

                // Mark as added
                this.added = true;

                // If we're adding to renderer root, or other elements in the group
                // have a z index, we need to handle it
                if (!parent || parent.handleZ || this.zIndex) {
                    inserted = this.zIndexSetter();
                }

                // If zIndex is not handled, append at the end
                if (!inserted) {
                    (parent ? parent.element : renderer.box).appendChild(element);
                }

                // fire an event for internal hooks
                if (this.onAdd) {
                    this.onAdd();
                }

                return this;
            },

            /**
             * Removes an element from the DOM.
             *
             * @private
             * @param {SVGDOMElement|HTMLDOMElement} element The DOM node to remove.
             */
            safeRemoveChild: function(element) {
                var parentNode = element.parentNode;
                if (parentNode) {
                    parentNode.removeChild(element);
                }
            },

            /**
             * Destroy the element and element wrapper and clear up the DOM and event
             * hooks.
             *
             * @returns {void}
             */
            destroy: function() {
                var wrapper = this,
                    element = wrapper.element || {},
                    parentToClean = wrapper.renderer.isSVG && element.nodeName === 'SPAN' && wrapper.parentGroup,
                    grandParent,
                    key,
                    i;

                // remove events
                element.onclick = element.onmouseout = element.onmouseover = element.onmousemove = element.point = null;
                stop(wrapper); // stop running animations

                if (wrapper.clipPath) {
                    wrapper.clipPath = wrapper.clipPath.destroy();
                }

                // Destroy stops in case this is a gradient object
                if (wrapper.stops) {
                    for (i = 0; i < wrapper.stops.length; i++) {
                        wrapper.stops[i] = wrapper.stops[i].destroy();
                    }
                    wrapper.stops = null;
                }

                // remove element
                wrapper.safeRemoveChild(element);


                wrapper.destroyShadows();


                // In case of useHTML, clean up empty containers emulating SVG groups (#1960, #2393, #2697).
                while (parentToClean && parentToClean.div && parentToClean.div.childNodes.length === 0) {
                    grandParent = parentToClean.parentGroup;
                    wrapper.safeRemoveChild(parentToClean.div);
                    delete parentToClean.div;
                    parentToClean = grandParent;
                }

                // remove from alignObjects
                if (wrapper.alignTo) {
                    erase(wrapper.renderer.alignedObjects, wrapper);
                }

                for (key in wrapper) {
                    delete wrapper[key];
                }

                return null;
            },


            /**
             * @typedef {Object} ShadowOptions
             * @property {string} [color=#000000] The shadow color.
             * @property {number} [offsetX=1] The horizontal offset from the element.
             * @property {number} [offsetY=1] The vertical offset from the element.
             * @property {number} [opacity=0.15] The shadow opacity.
             * @property {number} [width=3] The shadow width or distance from the
             *    element.
             */
            /**
             * Add a shadow to the element. Must be called after the element is added to
             * the DOM. In styled mode, this method is not used, instead use `defs` and
             * filters.
             * 
             * @param {boolean|ShadowOptions} shadowOptions The shadow options. If
             *    `true`, the default options are applied. If `false`, the current
             *    shadow will be removed.
             * @param {SVGElement} [group] The SVG group element where the shadows will 
             *    be applied. The default is to add it to the same parent as the current
             *    element. Internally, this is ised for pie slices, where all the
             *    shadows are added to an element behind all the slices.
             * @param {boolean} [cutOff] Used internally for column shadows.
             *
             * @returns {SVGElement} Returns the SVGElement for chaining.
             *
             * @example
             * renderer.rect(10, 100, 100, 100)
             *     .attr({ fill: 'red' })
             *     .shadow(true);
             */
            shadow: function(shadowOptions, group, cutOff) {
                var shadows = [],
                    i,
                    shadow,
                    element = this.element,
                    strokeWidth,
                    shadowWidth,
                    shadowElementOpacity,

                    // compensate for inverted plot area
                    transform;

                if (!shadowOptions) {
                    this.destroyShadows();

                } else if (!this.shadows) {
                    shadowWidth = pick(shadowOptions.width, 3);
                    shadowElementOpacity = (shadowOptions.opacity || 0.15) / shadowWidth;
                    transform = this.parentInverted ?
                        '(-1,-1)' :
                        '(' + pick(shadowOptions.offsetX, 1) + ', ' + pick(shadowOptions.offsetY, 1) + ')';
                    for (i = 1; i <= shadowWidth; i++) {
                        shadow = element.cloneNode(0);
                        strokeWidth = (shadowWidth * 2) + 1 - (2 * i);
                        attr(shadow, {
                            'isShadow': 'true',
                            'stroke': shadowOptions.color || '#000000',
                            'stroke-opacity': shadowElementOpacity * i,
                            'stroke-width': strokeWidth,
                            'transform': 'translate' + transform,
                            'fill': 'none'
                        });
                        if (cutOff) {
                            attr(shadow, 'height', Math.max(attr(shadow, 'height') - strokeWidth, 0));
                            shadow.cutHeight = strokeWidth;
                        }

                        if (group) {
                            group.element.appendChild(shadow);
                        } else {
                            element.parentNode.insertBefore(shadow, element);
                        }

                        shadows.push(shadow);
                    }

                    this.shadows = shadows;
                }
                return this;

            },

            /**
             * Destroy shadows on the element.
             * @private
             */
            destroyShadows: function() {
                each(this.shadows || [], function(shadow) {
                    this.safeRemoveChild(shadow);
                }, this);
                this.shadows = undefined;
            },



            xGetter: function(key) {
                if (this.element.nodeName === 'circle') {
                    if (key === 'x') {
                        key = 'cx';
                    } else if (key === 'y') {
                        key = 'cy';
                    }
                }
                return this._defaultGetter(key);
            },

            /**
             * Get the current value of an attribute or pseudo attribute, used mainly
             * for animation. Called internally from the {@link SVGRenderer#attr}
             * function.
             *
             * @private
             */
            _defaultGetter: function(key) {
                var ret = pick(this[key], this.element ? this.element.getAttribute(key) : null, 0);

                if (/^[\-0-9\.]+$/.test(ret)) { // is numerical
                    ret = parseFloat(ret);
                }
                return ret;
            },


            dSetter: function(value, key, element) {
                if (value && value.join) { // join path
                    value = value.join(' ');
                }
                if (/(NaN| {2}|^$)/.test(value)) {
                    value = 'M 0 0';
                }
                element.setAttribute(key, value);

                this[key] = value;
            },

            dashstyleSetter: function(value) {
                var i,
                    strokeWidth = this['stroke-width'];

                // If "inherit", like maps in IE, assume 1 (#4981). With HC5 and the new strokeWidth 
                // function, we should be able to use that instead.
                if (strokeWidth === 'inherit') {
                    strokeWidth = 1;
                }
                value = value && value.toLowerCase();
                if (value) {
                    value = value
                        .replace('shortdashdotdot', '3,1,1,1,1,1,')
                        .replace('shortdashdot', '3,1,1,1')
                        .replace('shortdot', '1,1,')
                        .replace('shortdash', '3,1,')
                        .replace('longdash', '8,3,')
                        .replace(/dot/g, '1,3,')
                        .replace('dash', '4,3,')
                        .replace(/,$/, '')
                        .split(','); // ending comma

                    i = value.length;
                    while (i--) {
                        value[i] = pInt(value[i]) * strokeWidth;
                    }
                    value = value.join(',')
                        .replace(/NaN/g, 'none'); // #3226
                    this.element.setAttribute('stroke-dasharray', value);
                }
            },

            alignSetter: function(value) {
                var convert = {
                    left: 'start',
                    center: 'middle',
                    right: 'end'
                };
                this.element.setAttribute('text-anchor', convert[value]);
            },
            opacitySetter: function(value, key, element) {
                this[key] = value;
                element.setAttribute(key, value);
            },
            titleSetter: function(value) {
                var titleNode = this.element.getElementsByTagName('title')[0];
                if (!titleNode) {
                    titleNode = doc.createElementNS(this.SVG_NS, 'title');
                    this.element.appendChild(titleNode);
                }

                // Remove text content if it exists
                if (titleNode.firstChild) {
                    titleNode.removeChild(titleNode.firstChild);
                }

                titleNode.appendChild(
                    doc.createTextNode(
                        (String(pick(value), '')).replace(/<[^>]*>/g, '') // #3276, #3895
                    )
                );
            },
            textSetter: function(value) {
                if (value !== this.textStr) {
                    // Delete bBox memo when the text changes
                    delete this.bBox;

                    this.textStr = value;
                    if (this.added) {
                        this.renderer.buildText(this);
                    }
                }
            },
            fillSetter: function(value, key, element) {
                if (typeof value === 'string') {
                    element.setAttribute(key, value);
                } else if (value) {
                    this.colorGradient(value, key, element);
                }
            },
            visibilitySetter: function(value, key, element) {
                // IE9-11 doesn't handle visibilty:inherit well, so we remove the attribute instead (#2881, #3909)
                if (value === 'inherit') {
                    element.removeAttribute(key);
                } else {
                    element.setAttribute(key, value);
                }
            },
            zIndexSetter: function(value, key) {
                var renderer = this.renderer,
                    parentGroup = this.parentGroup,
                    parentWrapper = parentGroup || renderer,
                    parentNode = parentWrapper.element || renderer.box,
                    childNodes,
                    otherElement,
                    otherZIndex,
                    element = this.element,
                    inserted,
                    run = this.added,
                    i;

                if (defined(value)) {
                    element.zIndex = value; // So we can read it for other elements in the group
                    value = +value;
                    if (this[key] === value) { // Only update when needed (#3865)
                        run = false;
                    }
                    this[key] = value;
                }

                // Insert according to this and other elements' zIndex. Before .add() is called,
                // nothing is done. Then on add, or by later calls to zIndexSetter, the node
                // is placed on the right place in the DOM.
                if (run) {
                    value = this.zIndex;

                    if (value && parentGroup) {
                        parentGroup.handleZ = true;
                    }

                    childNodes = parentNode.childNodes;
                    for (i = 0; i < childNodes.length && !inserted; i++) {
                        otherElement = childNodes[i];
                        otherZIndex = otherElement.zIndex;
                        if (otherElement !== element && (
                                // Insert before the first element with a higher zIndex
                                pInt(otherZIndex) > value ||
                                // If no zIndex given, insert before the first element with a zIndex
                                (!defined(value) && defined(otherZIndex)) ||
                                // Negative zIndex versus no zIndex:
                                // On all levels except the highest. If the parent is <svg>,
                                // then we don't want to put items before <desc> or <defs>
                                (value < 0 && !defined(otherZIndex) && parentNode !== renderer.box)

                            )) {
                            parentNode.insertBefore(element, otherElement);
                            inserted = true;
                        }
                    }
                    if (!inserted) {
                        parentNode.appendChild(element);
                    }
                }
                return inserted;
            },
            _defaultSetter: function(value, key, element) {
                element.setAttribute(key, value);
            }
        };

        // Some shared setters and getters
        SVGElement.prototype.yGetter = SVGElement.prototype.xGetter;
        SVGElement.prototype.translateXSetter = SVGElement.prototype.translateYSetter =
            SVGElement.prototype.rotationSetter = SVGElement.prototype.verticalAlignSetter =
            SVGElement.prototype.scaleXSetter = SVGElement.prototype.scaleYSetter = function(value, key) {
                this[key] = value;
                this.doTransform = true;
            };


        // WebKit and Batik have problems with a stroke-width of zero, so in this case we remove the 
        // stroke attribute altogether. #1270, #1369, #3065, #3072.
        SVGElement.prototype['stroke-widthSetter'] = SVGElement.prototype.strokeSetter = function(value, key, element) {
            this[key] = value;
            // Only apply the stroke attribute if the stroke width is defined and larger than 0
            if (this.stroke && this['stroke-width']) {
                SVGElement.prototype.fillSetter.call(this, this.stroke, 'stroke', element); // use prototype as instance may be overridden
                element.setAttribute('stroke-width', this['stroke-width']);
                this.hasStroke = true;
            } else if (key === 'stroke-width' && value === 0 && this.hasStroke) {
                element.removeAttribute('stroke');
                this.hasStroke = false;
            }
        };


        /**
         * Allows direct access to the Highcharts rendering layer in order to draw
         * primitive shapes like circles, rectangles, paths or text directly on a chart,
         * or independent from any chart. The SVGRenderer represents a wrapper object
         * for SVGin modern browsers and through the VMLRenderer, for VML in IE < 8.
         *
         * An existing chart's renderer can be accessed through {@link Chart#renderer}.
         * The renderer can also be used completely decoupled from a chart.
         *
         * @param {HTMLDOMElement} container - Where to put the SVG in the web page.
         * @param {number} width - The width of the SVG.
         * @param {number} height - The height of the SVG.
         * @param {boolean} [forExport=false] - Whether the rendered content is intended
         *   for export.
         * @param {boolean} [allowHTML=true] - Whether the renderer is allowed to
         *   include HTML text, which will be projected on top of the SVG.
         *
         * @example
         * // Use directly without a chart object.
         * var renderer = new Highcharts.Renderer(parentNode, 600, 400);
         *
         * @sample highcharts/members/renderer-on-chart - Annotating a chart programmatically.
         * @sample highcharts/members/renderer-basic - Independedt SVG drawing.
         *
         * @class
         */
        SVGRenderer = H.SVGRenderer = function() {
            this.init.apply(this, arguments);
        };
        SVGRenderer.prototype = {
            /**
             * A pointer to the renderer's associated Element class. The VMLRenderer
             * will have a pointer to VMLElement here.
             * @type {SVGElement}
             */
            Element: SVGElement,
            SVG_NS: SVG_NS,
            /**
             * Initialize the SVGRenderer. Overridable initiator function that takes
             * the same parameters as the constructor.
             */
            init: function(container, width, height, style, forExport, allowHTML) {
                var renderer = this,
                    boxWrapper,
                    element,
                    desc;

                boxWrapper = renderer.createElement('svg')
                    .attr({
                        'version': '1.1',
                        'class': 'highcharts-root'
                    })

                .css(this.getStyle(style));
                element = boxWrapper.element;
                container.appendChild(element);

                // For browsers other than IE, add the namespace attribute (#1978)
                if (container.innerHTML.indexOf('xmlns') === -1) {
                    attr(element, 'xmlns', this.SVG_NS);
                }

                // object properties
                renderer.isSVG = true;

                /** 
                 * The root `svg` node of the renderer.
                 * @type {SVGDOMElement}
                 */
                this.box = element;
                /** 
                 * The wrapper for the root `svg` node of the renderer.
                 * @type {SVGElement}
                 */
                this.boxWrapper = boxWrapper;
                renderer.alignedObjects = [];

                /**
                 * Page url used for internal references.
                 * @type {string}
                 */
                // #24, #672, #1070
                this.url = (isFirefox || isWebKit) && doc.getElementsByTagName('base').length ?
                    win.location.href
                    .replace(/#.*?$/, '') // remove the hash
                    .replace(/([\('\)])/g, '\\$1') // escape parantheses and quotes
                    .replace(/ /g, '%20') : // replace spaces (needed for Safari only)
                    '';

                // Add description
                desc = this.createElement('desc').add();
                desc.element.appendChild(doc.createTextNode('Created with Highcharts 5.0.4'));


                renderer.defs = this.createElement('defs').add();
                renderer.allowHTML = allowHTML;
                renderer.forExport = forExport;
                renderer.gradients = {}; // Object where gradient SvgElements are stored
                renderer.cache = {}; // Cache for numerical bounding boxes
                renderer.cacheKeys = [];
                renderer.imgCount = 0;

                renderer.setSize(width, height, false);



                // Issue 110 workaround:
                // In Firefox, if a div is positioned by percentage, its pixel position may land
                // between pixels. The container itself doesn't display this, but an SVG element
                // inside this container will be drawn at subpixel precision. In order to draw
                // sharp lines, this must be compensated for. This doesn't seem to work inside
                // iframes though (like in jsFiddle).
                var subPixelFix, rect;
                if (isFirefox && container.getBoundingClientRect) {
                    subPixelFix = function() {
                        css(container, {
                            left: 0,
                            top: 0
                        });
                        rect = container.getBoundingClientRect();
                        css(container, {
                            left: (Math.ceil(rect.left) - rect.left) + 'px',
                            top: (Math.ceil(rect.top) - rect.top) + 'px'
                        });
                    };

                    // run the fix now
                    subPixelFix();

                    // run it on resize
                    renderer.unSubPixelFix = addEvent(win, 'resize', subPixelFix);
                }
            },



            /**
             * Get the global style setting for the renderer.
             * @private
             * @param  {CSSObject} style - Style settings.
             * @return {CSSObject} The style settings mixed with defaults.
             */
            getStyle: function(style) {
                this.style = extend({

                    fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif', // default font
                    fontSize: '12px'

                }, style);
                return this.style;
            },
            /**
             * Apply the global style on the renderer, mixed with the default styles.
             * @param {CSSObject} style - CSS to apply.
             */
            setStyle: function(style) {
                this.boxWrapper.css(this.getStyle(style));
            },


            /**
             * Detect whether the renderer is hidden. This happens when one of the
             * parent elements has display: none. Used internally to detect when we need
             * to render preliminarily in another div to get the text bounding boxes 
             * right.
             *
             * @returns {boolean} True if it is hidden.
             */
            isHidden: function() { // #608
                return !this.boxWrapper.getBBox().width;
            },

            /**
             * Destroys the renderer and its allocated members.
             */
            destroy: function() {
                var renderer = this,
                    rendererDefs = renderer.defs;
                renderer.box = null;
                renderer.boxWrapper = renderer.boxWrapper.destroy();

                // Call destroy on all gradient elements
                destroyObjectProperties(renderer.gradients || {});
                renderer.gradients = null;

                // Defs are null in VMLRenderer
                // Otherwise, destroy them here.
                if (rendererDefs) {
                    renderer.defs = rendererDefs.destroy();
                }

                // Remove sub pixel fix handler (#982)
                if (renderer.unSubPixelFix) {
                    renderer.unSubPixelFix();
                }

                renderer.alignedObjects = null;

                return null;
            },

            /**
             * Create a wrapper for an SVG element. Serves as a factory for 
             * {@link SVGElement}, but this function is itself mostly called from 
             * primitive factories like {@link SVGRenderer#path}, {@link
             * SVGRenderer#rect} or {@link SVGRenderer#text}.
             * 
             * @param {string} nodeName - The node name, for example `rect`, `g` etc.
             * @returns {SVGElement} The generated SVGElement.
             */
            createElement: function(nodeName) {
                var wrapper = new this.Element();
                wrapper.init(this, nodeName);
                return wrapper;
            },

            /**
             * Dummy function for plugins, called every time the renderer is updated.
             * Prior to Highcharts 5, this was used for the canvg renderer.
             * @function
             */
            draw: noop,

            /**
             * Get converted radial gradient attributes according to the radial
             * reference. Used internally from the {@link SVGElement#colorGradient}
             * function.
             *
             * @private
             */
            getRadialAttr: function(radialReference, gradAttr) {
                return {
                    cx: (radialReference[0] - radialReference[2] / 2) + gradAttr.cx * radialReference[2],
                    cy: (radialReference[1] - radialReference[2] / 2) + gradAttr.cy * radialReference[2],
                    r: gradAttr.r * radialReference[2]
                };
            },

            /**
             * Parse a simple HTML string into SVG tspans. Called internally when text
             *   is set on an SVGElement. The function supports a subset of HTML tags,
             *   CSS text features like `width`, `text-overflow`, `white-space`, and
             *   also attributes like `href` and `style`.
             * @private
             * @param {SVGElement} wrapper The parent SVGElement.
             */
            buildText: function(wrapper) {
                var textNode = wrapper.element,
                    renderer = this,
                    forExport = renderer.forExport,
                    textStr = pick(wrapper.textStr, '').toString(),
                    hasMarkup = textStr.indexOf('<') !== -1,
                    lines,
                    childNodes = textNode.childNodes,
                    clsRegex,
                    styleRegex,
                    hrefRegex,
                    wasTooLong,
                    parentX = attr(textNode, 'x'),
                    textStyles = wrapper.styles,
                    width = wrapper.textWidth,
                    textLineHeight = textStyles && textStyles.lineHeight,
                    textOutline = textStyles && textStyles.textOutline,
                    ellipsis = textStyles && textStyles.textOverflow === 'ellipsis',
                    i = childNodes.length,
                    tempParent = width && !wrapper.added && this.box,
                    getLineHeight = function(tspan) {
                        var fontSizeStyle;

                        fontSizeStyle = /(px|em)$/.test(tspan && tspan.style.fontSize) ?
                            tspan.style.fontSize :
                            ((textStyles && textStyles.fontSize) || renderer.style.fontSize || 12);


                        return textLineHeight ?
                            pInt(textLineHeight) :
                            renderer.fontMetrics(
                                fontSizeStyle,
                                // Get the computed size from parent if not explicit
                                tspan.getAttribute('style') ? tspan : textNode
                            ).h;
                    },
                    unescapeAngleBrackets = function(inputStr) {
                        return inputStr.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
                    };

                /// remove old text
                while (i--) {
                    textNode.removeChild(childNodes[i]);
                }

                // Skip tspans, add text directly to text node. The forceTSpan is a hook
                // used in text outline hack.
                if (!hasMarkup && !textOutline && !ellipsis && !width && textStr.indexOf(' ') === -1) {
                    textNode.appendChild(doc.createTextNode(unescapeAngleBrackets(textStr)));

                    // Complex strings, add more logic
                } else {

                    clsRegex = /<.*class="([^"]+)".*>/;
                    styleRegex = /<.*style="([^"]+)".*>/;
                    hrefRegex = /<.*href="(http[^"]+)".*>/;

                    if (tempParent) {
                        tempParent.appendChild(textNode); // attach it to the DOM to read offset width
                    }

                    if (hasMarkup) {
                        lines = textStr
                            .replace(/<(b|strong)>/g, '<span style="font-weight:bold">')
                            .replace(/<(i|em)>/g, '<span style="font-style:italic">')
                            .replace(/<a/g, '<span')
                            .replace(/<\/(b|strong|i|em|a)>/g, '</span>')
                            .split(/<br.*?>/g);

                    } else {
                        lines = [textStr];
                    }


                    // Trim empty lines (#5261)
                    lines = grep(lines, function(line) {
                        return line !== '';
                    });


                    // build the lines
                    each(lines, function buildTextLines(line, lineNo) {
                        var spans,
                            spanNo = 0;
                        line = line
                            .replace(/^\s+|\s+$/g, '') // Trim to prevent useless/costly process on the spaces (#5258)
                            .replace(/<span/g, '|||<span')
                            .replace(/<\/span>/g, '</span>|||');
                        spans = line.split('|||');

                        each(spans, function buildTextSpans(span) {
                            if (span !== '' || spans.length === 1) {
                                var attributes = {},
                                    tspan = doc.createElementNS(renderer.SVG_NS, 'tspan'),
                                    spanCls,
                                    spanStyle; // #390
                                if (clsRegex.test(span)) {
                                    spanCls = span.match(clsRegex)[1];
                                    attr(tspan, 'class', spanCls);
                                }
                                if (styleRegex.test(span)) {
                                    spanStyle = span.match(styleRegex)[1].replace(/(;| |^)color([ :])/, '$1fill$2');
                                    attr(tspan, 'style', spanStyle);
                                }
                                if (hrefRegex.test(span) && !forExport) { // Not for export - #1529
                                    attr(tspan, 'onclick', 'location.href=\"' + span.match(hrefRegex)[1] + '\"');
                                    css(tspan, {
                                        cursor: 'pointer'
                                    });
                                }

                                span = unescapeAngleBrackets(span.replace(/<(.|\n)*?>/g, '') || ' ');

                                // Nested tags aren't supported, and cause crash in Safari (#1596)
                                if (span !== ' ') {

                                    // add the text node
                                    tspan.appendChild(doc.createTextNode(span));

                                    if (!spanNo) { // first span in a line, align it to the left
                                        if (lineNo && parentX !== null) {
                                            attributes.x = parentX;
                                        }
                                    } else {
                                        attributes.dx = 0; // #16
                                    }

                                    // add attributes
                                    attr(tspan, attributes);

                                    // Append it
                                    textNode.appendChild(tspan);

                                    // first span on subsequent line, add the line height
                                    if (!spanNo && lineNo) {

                                        // allow getting the right offset height in exporting in IE
                                        if (!svg && forExport) {
                                            css(tspan, {
                                                display: 'block'
                                            });
                                        }

                                        // Set the line height based on the font size of either
                                        // the text element or the tspan element
                                        attr(
                                            tspan,
                                            'dy',
                                            getLineHeight(tspan)
                                        );
                                    }

                                    /*if (width) {
                                    	renderer.breakText(wrapper, width);
                                    }*/

                                    // Check width and apply soft breaks or ellipsis
                                    if (width) {
                                        var words = span.replace(/([^\^])-/g, '$1- ').split(' '), // #1273
                                            noWrap = textStyles.whiteSpace === 'nowrap',
                                            hasWhiteSpace = spans.length > 1 || lineNo || (words.length > 1 && !noWrap),
                                            tooLong,
                                            actualWidth,
                                            rest = [],
                                            dy = getLineHeight(tspan),
                                            rotation = wrapper.rotation,
                                            wordStr = span, // for ellipsis
                                            cursor = wordStr.length, // binary search cursor
                                            bBox;

                                        while ((hasWhiteSpace || ellipsis) && (words.length || rest.length)) {
                                            wrapper.rotation = 0; // discard rotation when computing box
                                            bBox = wrapper.getBBox(true);
                                            actualWidth = bBox.width;

                                            // Old IE cannot measure the actualWidth for SVG elements (#2314)
                                            if (!svg && renderer.forExport) {
                                                actualWidth = renderer.measureSpanWidth(tspan.firstChild.data, wrapper.styles);
                                            }

                                            tooLong = actualWidth > width;

                                            // For ellipsis, do a binary search for the correct string length
                                            if (wasTooLong === undefined) {
                                                wasTooLong = tooLong; // First time
                                            }
                                            if (ellipsis && wasTooLong) {
                                                cursor /= 2;

                                                if (wordStr === '' || (!tooLong && cursor < 0.5)) {
                                                    words = []; // All ok, break out
                                                } else {
                                                    wordStr = span.substring(0, wordStr.length + (tooLong ? -1 : 1) * Math.ceil(cursor));
                                                    words = [wordStr + (width > 3 ? '\u2026' : '')];
                                                    tspan.removeChild(tspan.firstChild);
                                                }

                                                // Looping down, this is the first word sequence that is not too long,
                                                // so we can move on to build the next line.
                                            } else if (!tooLong || words.length === 1) {
                                                words = rest;
                                                rest = [];

                                                if (words.length && !noWrap) {
                                                    tspan = doc.createElementNS(SVG_NS, 'tspan');
                                                    attr(tspan, {
                                                        dy: dy,
                                                        x: parentX
                                                    });
                                                    if (spanStyle) { // #390
                                                        attr(tspan, 'style', spanStyle);
                                                    }
                                                    textNode.appendChild(tspan);
                                                }
                                                if (actualWidth > width) { // a single word is pressing it out
                                                    width = actualWidth;
                                                }
                                            } else { // append to existing line tspan
                                                tspan.removeChild(tspan.firstChild);
                                                rest.unshift(words.pop());
                                            }
                                            if (words.length) {
                                                tspan.appendChild(doc.createTextNode(words.join(' ').replace(/- /g, '-')));
                                            }
                                        }
                                        wrapper.rotation = rotation;
                                    }

                                    spanNo++;
                                }
                            }
                        });
                    });

                    if (wasTooLong) {
                        wrapper.attr('title', wrapper.textStr);
                    }
                    if (tempParent) {
                        tempParent.removeChild(textNode); // attach it to the DOM to read offset width
                    }

                    // Apply the text outline
                    if (textOutline && wrapper.applyTextOutline) {
                        wrapper.applyTextOutline(textOutline);
                    }
                }
            },



            /*
            breakText: function (wrapper, width) {
            	var bBox = wrapper.getBBox(),
            		node = wrapper.element,
            		textLength = node.textContent.length,
            		pos = Math.round(width * textLength / bBox.width), // try this position first, based on average character width
            		increment = 0,
            		finalPos;

            	if (bBox.width > width) {
            		while (finalPos === undefined) {
            			textLength = node.getSubStringLength(0, pos);

            			if (textLength <= width) {
            				if (increment === -1) {
            					finalPos = pos;
            				} else {
            					increment = 1;
            				}
            			} else {
            				if (increment === 1) {
            					finalPos = pos - 1;
            				} else {
            					increment = -1;
            				}
            			}
            			pos += increment;
            		}
            	}
            	console.log('width', width, 'stringWidth', node.getSubStringLength(0, finalPos))
            },
            */

            /**
             * Returns white for dark colors and black for bright colors.
             *
             * @param {ColorString} rgba - The color to get the contrast for.
             * @returns {string} The contrast color, either `#000000` or `#FFFFFF`.
             */
            getContrast: function(rgba) {
                rgba = color(rgba).rgba;
                return rgba[0] + rgba[1] + rgba[2] > 2 * 255 ? '#000000' : '#FFFFFF';
            },

            /**
             * Create a button with preset states.
             * @param {string} text - The text or HTML to draw.
             * @param {number} x - The x position of the button's left side.
             * @param {number} y - The y position of the button's top side.
             * @param {Function} callback - The function to execute on button click or 
             *    touch.
             * @param {SVGAttributes} [normalState] - SVG attributes for the normal
             *    state.
             * @param {SVGAttributes} [hoverState] - SVG attributes for the hover state.
             * @param {SVGAttributes} [pressedState] - SVG attributes for the pressed
             *    state.
             * @param {SVGAttributes} [disabledState] - SVG attributes for the disabled
             *    state.
             * @param {Symbol} [shape=rect] - The shape type.
             * @returns {SVGRenderer} The button element.
             */
            button: function(text, x, y, callback, normalState, hoverState, pressedState, disabledState, shape) {
                var label = this.label(text, x, y, shape, null, null, null, null, 'button'),
                    curState = 0;

                // Default, non-stylable attributes
                label.attr(merge({
                    'padding': 8,
                    'r': 2
                }, normalState));


                // Presentational
                var normalStyle,
                    hoverStyle,
                    pressedStyle,
                    disabledStyle;

                // Normal state - prepare the attributes
                normalState = merge({
                    fill: '#f7f7f7',
                    stroke: '#cccccc',
                    'stroke-width': 1,
                    style: {
                        color: '#333333',
                        cursor: 'pointer',
                        fontWeight: 'normal'
                    }
                }, normalState);
                normalStyle = normalState.style;
                delete normalState.style;

                // Hover state
                hoverState = merge(normalState, {
                    fill: '#e6e6e6'
                }, hoverState);
                hoverStyle = hoverState.style;
                delete hoverState.style;

                // Pressed state
                pressedState = merge(normalState, {
                    fill: '#e6ebf5',
                    style: {
                        color: '#000000',
                        fontWeight: 'bold'
                    }
                }, pressedState);
                pressedStyle = pressedState.style;
                delete pressedState.style;

                // Disabled state
                disabledState = merge(normalState, {
                    style: {
                        color: '#cccccc'
                    }
                }, disabledState);
                disabledStyle = disabledState.style;
                delete disabledState.style;


                // Add the events. IE9 and IE10 need mouseover and mouseout to funciton (#667).
                addEvent(label.element, isMS ? 'mouseover' : 'mouseenter', function() {
                    if (curState !== 3) {
                        label.setState(1);
                    }
                });
                addEvent(label.element, isMS ? 'mouseout' : 'mouseleave', function() {
                    if (curState !== 3) {
                        label.setState(curState);
                    }
                });

                label.setState = function(state) {
                    // Hover state is temporary, don't record it
                    if (state !== 1) {
                        label.state = curState = state;
                    }
                    // Update visuals
                    label.removeClass(/highcharts-button-(normal|hover|pressed|disabled)/)
                        .addClass('highcharts-button-' + ['normal', 'hover', 'pressed', 'disabled'][state || 0]);


                    label.attr([normalState, hoverState, pressedState, disabledState][state || 0])
                        .css([normalStyle, hoverStyle, pressedStyle, disabledStyle][state || 0]);

                };



                // Presentational attributes
                label
                    .attr(normalState)
                    .css(extend({
                        cursor: 'default'
                    }, normalStyle));


                return label
                    .on('click', function(e) {
                        if (curState !== 3) {
                            callback.call(label, e);
                        }
                    });
            },

            /**
             * Make a straight line crisper by not spilling out to neighbour pixels.
             * 
             * @param {Array} points - The original points on the format `['M', 0, 0,
             *    'L', 100, 0]`.
             * @param {number} width - The width of the line.
             * @returns {Array} The original points array, but modified to render
             * crisply.
             */
            crispLine: function(points, width) {
                // normalize to a crisp line
                if (points[1] === points[4]) {
                    // Substract due to #1129. Now bottom and left axis gridlines behave the same.
                    points[1] = points[4] = Math.round(points[1]) - (width % 2 / 2);
                }
                if (points[2] === points[5]) {
                    points[2] = points[5] = Math.round(points[2]) + (width % 2 / 2);
                }
                return points;
            },


            /**
             * Draw a path, wraps the SVG `path` element.
             * 
             * @param {Array} [path] An SVG path definition in array form.
             * 
             * @example
             * var path = renderer.path(['M', 10, 10, 'L', 30, 30, 'z'])
             *     .attr({ stroke: '#ff00ff' })
             *     .add();
             * @returns {SVGElement} The generated wrapper element.
             */
            /**
             * Draw a path, wraps the SVG `path` element.
             * 
             * @param {SVGAttributes} [attribs] The initial attributes.
             * @returns {SVGElement} The generated wrapper element.
             */
            path: function(path) {
                var attribs = {

                    fill: 'none'

                };
                if (isArray(path)) {
                    attribs.d = path;
                } else if (isObject(path)) { // attributes
                    extend(attribs, path);
                }
                return this.createElement('path').attr(attribs);
            },

            /**
             * Draw a circle, wraps the SVG `circle` element.
             * 
             * @param {number} [x] The center x position.
             * @param {number} [y] The center y position.
             * @param {number} [r] The radius.
             * @returns {SVGElement} The generated wrapper element.
             */
            /**
             * Draw a circle, wraps the SVG `circle` element.
             * 
             * @param {SVGAttributes} [attribs] The initial attributes.
             * @returns {SVGElement} The generated wrapper element.
             */
            circle: function(x, y, r) {
                var attribs = isObject(x) ? x : {
                        x: x,
                        y: y,
                        r: r
                    },
                    wrapper = this.createElement('circle');

                // Setting x or y translates to cx and cy
                wrapper.xSetter = wrapper.ySetter = function(value, key, element) {
                    element.setAttribute('c' + key, value);
                };

                return wrapper.attr(attribs);
            },

            /**
             * Draw and return an arc.
             * @param {number} [x=0] Center X position.
             * @param {number} [y=0] Center Y position.
             * @param {number} [r=0] The outer radius of the arc.
             * @param {number} [innerR=0] Inner radius like used in donut charts.
             * @param {number} [start=0] The starting angle of the arc in radians, where
             *    0 is to the right and `-Math.PI/2` is up.
             * @param {number} [end=0] The ending angle of the arc in radians, where 0
             *    is to the right and `-Math.PI/2` is up.
             * @returns {SVGElement} The generated wrapper element.
             */
            /**
             * Draw and return an arc. Overloaded function that takes arguments object.
             * @param {SVGAttributes} attribs Initial SVG attributes.
             * @returns {SVGElement} The generated wrapper element.
             */
            arc: function(x, y, r, innerR, start, end) {
                var arc;

                if (isObject(x)) {
                    y = x.y;
                    r = x.r;
                    innerR = x.innerR;
                    start = x.start;
                    end = x.end;
                    x = x.x;
                }

                // Arcs are defined as symbols for the ability to set
                // attributes in attr and animate
                arc = this.symbol('arc', x || 0, y || 0, r || 0, r || 0, {
                    innerR: innerR || 0,
                    start: start || 0,
                    end: end || 0
                });
                arc.r = r; // #959
                return arc;
            },

            /**
             * Draw and return a rectangle.
             * @param {number} [x] Left position.
             * @param {number} [y] Top position.
             * @param {number} [width] Width of the rectangle.
             * @param {number} [height] Height of the rectangle.
             * @param {number} [r] Border corner radius.
             * @param {number} [strokeWidth] A stroke width can be supplied to allow
             *    crisp drawing.
             * @returns {SVGElement} The generated wrapper element.
             */
            /**
             * Draw and return a rectangle.
             * @param {SVGAttributes} [attributes] General SVG attributes for the 
             *    rectangle.
             * @returns {SVGElement} The generated wrapper element.
             */
            rect: function(x, y, width, height, r, strokeWidth) {

                r = isObject(x) ? x.r : r;

                var wrapper = this.createElement('rect'),
                    attribs = isObject(x) ? x : x === undefined ? {} : {
                        x: x,
                        y: y,
                        width: Math.max(width, 0),
                        height: Math.max(height, 0)
                    };


                if (strokeWidth !== undefined) {
                    attribs.strokeWidth = strokeWidth;
                    attribs = wrapper.crisp(attribs);
                }
                attribs.fill = 'none';


                if (r) {
                    attribs.r = r;
                }

                wrapper.rSetter = function(value, key, element) {
                    attr(element, {
                        rx: value,
                        ry: value
                    });
                };

                return wrapper.attr(attribs);
            },

            /**
             * Resize the {@link SVGRenderer#box} and re-align all aligned child
             * elements.
             * @param {number} width The new pixel width.
             * @param {number} height The new pixel height.
             * @param {boolean} animate Whether to animate.
             */
            setSize: function(width, height, animate) {
                var renderer = this,
                    alignedObjects = renderer.alignedObjects,
                    i = alignedObjects.length;

                renderer.width = width;
                renderer.height = height;

                renderer.boxWrapper.animate({
                    width: width,
                    height: height
                }, {
                    step: function() {
                        this.attr({
                            viewBox: '0 0 ' + this.attr('width') + ' ' + this.attr('height')
                        });
                    },
                    duration: pick(animate, true) ? undefined : 0
                });

                while (i--) {
                    alignedObjects[i].align();
                }
            },

            /**
             * Create and return an svg group element.
             * 
             * @param {string} [name] The group will be given a class name of
             * `highcharts-{name}`. This can be used for styling and scripting.
             * @returns {SVGElement} The generated wrapper element.
             */
            g: function(name) {
                var elem = this.createElement('g');
                return name ? elem.attr({
                    'class': 'highcharts-' + name
                }) : elem;
            },

            /**
             * Display an image.
             * @param {string} src The image source.
             * @param {number} [x] The X position.
             * @param {number} [y] The Y position.
             * @param {number} [width] The image width. If omitted, it defaults to the 
             *    image file width.
             * @param {number} [height] The image height. If omitted it defaults to the
             *    image file height.
             * @returns {SVGElement} The generated wrapper element.
             */
            image: function(src, x, y, width, height) {
                var attribs = {
                        preserveAspectRatio: 'none'
                    },
                    elemWrapper;

                // optional properties
                if (arguments.length > 1) {
                    extend(attribs, {
                        x: x,
                        y: y,
                        width: width,
                        height: height
                    });
                }

                elemWrapper = this.createElement('image').attr(attribs);

                // set the href in the xlink namespace
                if (elemWrapper.element.setAttributeNS) {
                    elemWrapper.element.setAttributeNS('http://www.w3.org/1999/xlink',
                        'href', src);
                } else {
                    // could be exporting in IE
                    // using href throws "not supported" in ie7 and under, requries regex shim to fix later
                    elemWrapper.element.setAttribute('hc-svg-href', src);
                }
                return elemWrapper;
            },

            /**
             * Draw a symbol out of pre-defined shape paths from {@SVGRenderer#symbols}.
             * It is used in Highcharts for point makers, which cake a `symbol` option,
             * and label and button backgrounds like in the tooltip and stock flags.
             *
             * @param {Symbol} symbol - The symbol name.
             * @param {number} x - The X coordinate for the top left position.
             * @param {number} y - The Y coordinate for the top left position.
             * @param {number} width - The pixel width.
             * @param {number} height - The pixel height.
             * @param {Object} [options] - Additional options, depending on the actual
             *    symbol drawn. 
             * @param {number} [options.anchorX] - The anchor X position for the
             *    `callout` symbol. This is where the chevron points to.
             * @param {number} [options.anchorY] - The anchor Y position for the
             *    `callout` symbol. This is where the chevron points to.
             * @param {number} [options.end] - The end angle of an `arc` symbol.
             * @param {boolean} [options.open] - Whether to draw `arc` symbol open or
             *    closed.
             * @param {number} [options.r] - The radius of an `arc` symbol, or the
             *    border radius for the `callout` symbol.
             * @param {number} [options.start] - The start angle of an `arc` symbol.
             */
            symbol: function(symbol, x, y, width, height, options) {

                var ren = this,
                    obj,

                    // get the symbol definition function
                    symbolFn = this.symbols[symbol],

                    // check if there's a path defined for this symbol
                    path = defined(x) && symbolFn && symbolFn(
                        Math.round(x),
                        Math.round(y),
                        width,
                        height,
                        options
                    ),
                    imageRegex = /^url\((.*?)\)$/,
                    imageSrc,
                    centerImage;

                if (symbolFn) {
                    obj = this.path(path);


                    obj.attr('fill', 'none');


                    // expando properties for use in animate and attr
                    extend(obj, {
                        symbolName: symbol,
                        x: x,
                        y: y,
                        width: width,
                        height: height
                    });
                    if (options) {
                        extend(obj, options);
                    }


                    // image symbols
                } else if (imageRegex.test(symbol)) {


                    imageSrc = symbol.match(imageRegex)[1];

                    // Create the image synchronously, add attribs async
                    obj = this.image(imageSrc);

                    // The image width is not always the same as the symbol width. The
                    // image may be centered within the symbol, as is the case when
                    // image shapes are used as label backgrounds, for example in flags.
                    obj.imgwidth = pick(
                        symbolSizes[imageSrc] && symbolSizes[imageSrc].width,
                        options && options.width
                    );
                    obj.imgheight = pick(
                        symbolSizes[imageSrc] && symbolSizes[imageSrc].height,
                        options && options.height
                    );
                    /**
                     * Set the size and position
                     */
                    centerImage = function() {
                        obj.attr({
                            width: obj.width,
                            height: obj.height
                        });
                    };

                    /**
                     * Width and height setters that take both the image's physical size
                     * and the label size into consideration, and translates the image
                     * to center within the label.
                     */
                    each(['width', 'height'], function(key) {
                        obj[key + 'Setter'] = function(value, key) {
                            var attribs = {},
                                imgSize = this['img' + key],
                                trans = key === 'width' ? 'translateX' : 'translateY';
                            this[key] = value;
                            if (defined(imgSize)) {
                                if (this.element) {
                                    this.element.setAttribute(key, imgSize);
                                }
                                if (!this.alignByTranslate) {
                                    attribs[trans] = ((this[key] || 0) - imgSize) / 2;
                                    this.attr(attribs);
                                }
                            }
                        };
                    });


                    if (defined(x)) {
                        obj.attr({
                            x: x,
                            y: y
                        });
                    }
                    obj.isImg = true;

                    if (defined(obj.imgwidth) && defined(obj.imgheight)) {
                        centerImage();
                    } else {
                        // Initialize image to be 0 size so export will still function if there's no cached sizes.
                        obj.attr({
                            width: 0,
                            height: 0
                        });

                        // Create a dummy JavaScript image to get the width and height. Due to a bug in IE < 8,
                        // the created element must be assigned to a variable in order to load (#292).
                        createElement('img', {
                            onload: function() {

                                var chart = charts[ren.chartIndex];

                                // Special case for SVGs on IE11, the width is not accessible until the image is
                                // part of the DOM (#2854).
                                if (this.width === 0) {
                                    css(this, {
                                        position: 'absolute',
                                        top: '-999em'
                                    });
                                    doc.body.appendChild(this);
                                }

                                // Center the image
                                symbolSizes[imageSrc] = { // Cache for next	
                                    width: this.width,
                                    height: this.height
                                };
                                obj.imgwidth = this.width;
                                obj.imgheight = this.height;

                                if (obj.element) {
                                    centerImage();
                                }

                                // Clean up after #2854 workaround.
                                if (this.parentNode) {
                                    this.parentNode.removeChild(this);
                                }

                                // Fire the load event when all external images are loaded
                                ren.imgCount--;
                                if (!ren.imgCount && chart && chart.onload) {
                                    chart.onload();
                                }
                            },
                            src: imageSrc
                        });
                        this.imgCount++;
                    }
                }

                return obj;
            },

            /**
             * @typedef {string} Symbol
             * 
             * Can be one of `arc`, `callout`, `circle`, `diamond`, `square`,
             * `triangle`, `triangle-down`. Symbols are used internally for point
             * markers, button and label borders and backgrounds, or custom shapes.
             * Extendable by adding to {@link SVGRenderer#symbols}.
             */
            /**
             * An extendable collection of functions for defining symbol paths.
             */
            symbols: {
                'circle': function(x, y, w, h) {
                    var cpw = 0.166 * w;
                    return [
                        'M', x + w / 2, y,
                        'C', x + w + cpw, y, x + w + cpw, y + h, x + w / 2, y + h,
                        'C', x - cpw, y + h, x - cpw, y, x + w / 2, y,
                        'Z'
                    ];
                },

                'square': function(x, y, w, h) {
                    return [
                        'M', x, y,
                        'L', x + w, y,
                        x + w, y + h,
                        x, y + h,
                        'Z'
                    ];
                },

                'triangle': function(x, y, w, h) {
                    return [
                        'M', x + w / 2, y,
                        'L', x + w, y + h,
                        x, y + h,
                        'Z'
                    ];
                },

                'triangle-down': function(x, y, w, h) {
                    return [
                        'M', x, y,
                        'L', x + w, y,
                        x + w / 2, y + h,
                        'Z'
                    ];
                },
                'diamond': function(x, y, w, h) {
                    return [
                        'M', x + w / 2, y,
                        'L', x + w, y + h / 2,
                        x + w / 2, y + h,
                        x, y + h / 2,
                        'Z'
                    ];
                },
                'arc': function(x, y, w, h, options) {
                    var start = options.start,
                        radius = options.r || w || h,
                        end = options.end - 0.001, // to prevent cos and sin of start and end from becoming equal on 360 arcs (related: #1561)
                        innerRadius = options.innerR,
                        open = options.open,
                        cosStart = Math.cos(start),
                        sinStart = Math.sin(start),
                        cosEnd = Math.cos(end),
                        sinEnd = Math.sin(end),
                        longArc = options.end - start < Math.PI ? 0 : 1;

                    return [
                        'M',
                        x + radius * cosStart,
                        y + radius * sinStart,
                        'A', // arcTo
                        radius, // x radius
                        radius, // y radius
                        0, // slanting
                        longArc, // long or short arc
                        1, // clockwise
                        x + radius * cosEnd,
                        y + radius * sinEnd,
                        open ? 'M' : 'L',
                        x + innerRadius * cosEnd,
                        y + innerRadius * sinEnd,
                        'A', // arcTo
                        innerRadius, // x radius
                        innerRadius, // y radius
                        0, // slanting
                        longArc, // long or short arc
                        0, // clockwise
                        x + innerRadius * cosStart,
                        y + innerRadius * sinStart,

                        open ? '' : 'Z' // close
                    ];
                },

                /**
                 * Callout shape used for default tooltips, also used for rounded rectangles in VML
                 */
                callout: function(x, y, w, h, options) {
                    var arrowLength = 6,
                        halfDistance = 6,
                        r = Math.min((options && options.r) || 0, w, h),
                        safeDistance = r + halfDistance,
                        anchorX = options && options.anchorX,
                        anchorY = options && options.anchorY,
                        path;

                    path = [
                        'M', x + r, y,
                        'L', x + w - r, y, // top side
                        'C', x + w, y, x + w, y, x + w, y + r, // top-right corner
                        'L', x + w, y + h - r, // right side
                        'C', x + w, y + h, x + w, y + h, x + w - r, y + h, // bottom-right corner
                        'L', x + r, y + h, // bottom side
                        'C', x, y + h, x, y + h, x, y + h - r, // bottom-left corner
                        'L', x, y + r, // left side
                        'C', x, y, x, y, x + r, y // top-left corner
                    ];

                    // Anchor on right side
                    if (anchorX && anchorX > w) {

                        // Chevron
                        if (anchorY > y + safeDistance && anchorY < y + h - safeDistance) {
                            path.splice(13, 3,
                                'L', x + w, anchorY - halfDistance,
                                x + w + arrowLength, anchorY,
                                x + w, anchorY + halfDistance,
                                x + w, y + h - r
                            );

                            // Simple connector
                        } else {
                            path.splice(13, 3,
                                'L', x + w, h / 2,
                                anchorX, anchorY,
                                x + w, h / 2,
                                x + w, y + h - r
                            );
                        }

                        // Anchor on left side
                    } else if (anchorX && anchorX < 0) {

                        // Chevron
                        if (anchorY > y + safeDistance && anchorY < y + h - safeDistance) {
                            path.splice(33, 3,
                                'L', x, anchorY + halfDistance,
                                x - arrowLength, anchorY,
                                x, anchorY - halfDistance,
                                x, y + r
                            );

                            // Simple connector
                        } else {
                            path.splice(33, 3,
                                'L', x, h / 2,
                                anchorX, anchorY,
                                x, h / 2,
                                x, y + r
                            );
                        }

                    } else if (anchorY && anchorY > h && anchorX > x + safeDistance && anchorX < x + w - safeDistance) { // replace bottom
                        path.splice(23, 3,
                            'L', anchorX + halfDistance, y + h,
                            anchorX, y + h + arrowLength,
                            anchorX - halfDistance, y + h,
                            x + r, y + h
                        );
                    } else if (anchorY && anchorY < 0 && anchorX > x + safeDistance && anchorX < x + w - safeDistance) { // replace top
                        path.splice(3, 3,
                            'L', anchorX - halfDistance, y,
                            anchorX, y - arrowLength,
                            anchorX + halfDistance, y,
                            w - r, y
                        );
                    }

                    return path;
                }
            },

            /**
             * @typedef {SVGElement} ClipRect - A clipping rectangle that can be applied
             * to one or more {@link SVGElement} instances. It is instanciated with the
             * {@link SVGRenderer#clipRect} function and applied with the {@link 
             * SVGElement#clip} function.
             *
             * @example
             * var circle = renderer.circle(100, 100, 100)
             *     .attr({ fill: 'red' })
             *     .add();
             * var clipRect = renderer.clipRect(100, 100, 100, 100);
             *
             * // Leave only the lower right quarter visible
             * circle.clip(clipRect);
             */
            /**
             * Define a clipping rectangle
             * @param {String} id
             * @param {number} x
             * @param {number} y
             * @param {number} width
             * @param {number} height
             * @returns {ClipRect} A clipping rectangle.
             */
            clipRect: function(x, y, width, height) {
                var wrapper,
                    id = H.uniqueKey(),

                    clipPath = this.createElement('clipPath').attr({
                        id: id
                    }).add(this.defs);

                wrapper = this.rect(x, y, width, height, 0).add(clipPath);
                wrapper.id = id;
                wrapper.clipPath = clipPath;
                wrapper.count = 0;

                return wrapper;
            },





            /**
             * Add text to the SVG object
             * @param {String} str
             * @param {number} x Left position
             * @param {number} y Top position
             * @param {Boolean} useHTML Use HTML to render the text
             */
            text: function(str, x, y, useHTML) {

                // declare variables
                var renderer = this,
                    fakeSVG = !svg && renderer.forExport,
                    wrapper,
                    attribs = {};

                if (useHTML && (renderer.allowHTML || !renderer.forExport)) {
                    return renderer.html(str, x, y);
                }

                attribs.x = Math.round(x || 0); // X is always needed for line-wrap logic
                if (y) {
                    attribs.y = Math.round(y);
                }
                if (str || str === 0) {
                    attribs.text = str;
                }

                wrapper = renderer.createElement('text')
                    .attr(attribs);

                // Prevent wrapping from creating false offsetWidths in export in legacy IE (#1079, #1063)
                if (fakeSVG) {
                    wrapper.css({
                        position: 'absolute'
                    });
                }

                if (!useHTML) {
                    wrapper.xSetter = function(value, key, element) {
                        var tspans = element.getElementsByTagName('tspan'),
                            tspan,
                            parentVal = element.getAttribute(key),
                            i;
                        for (i = 0; i < tspans.length; i++) {
                            tspan = tspans[i];
                            // If the x values are equal, the tspan represents a linebreak
                            if (tspan.getAttribute(key) === parentVal) {
                                tspan.setAttribute(key, value);
                            }
                        }
                        element.setAttribute(key, value);
                    };
                }

                return wrapper;
            },

            /**
             * Utility to return the baseline offset and total line height from the font
             * size.
             *
             * @param {?string} fontSize The current font size to inspect. If not given,
             *   the font size will be found from the DOM element.
             * @param {SVGElement|SVGDOMElement} [elem] The element to inspect for a
             *   current font size.
             * @returns {Object} An object containing `h`: the line height, `b`: the
             * baseline relative to the top of the box, and `f`: the font size.
             */
            fontMetrics: function(fontSize, elem) {
                var lineHeight,
                    baseline;


                fontSize = fontSize ||
                    // When the elem is a DOM element (#5932)
                    (elem && elem.style && elem.style.fontSize) ||
                    // Fall back on the renderer style default
                    (this.style && this.style.fontSize);



                // Handle different units
                if (/px/.test(fontSize)) {
                    fontSize = pInt(fontSize);
                } else if (/em/.test(fontSize)) {
                    // The em unit depends on parent items
                    fontSize = parseFloat(fontSize) *
                        (elem ? this.fontMetrics(null, elem.parentNode).f : 16);
                } else {
                    fontSize = 12;
                }

                // Empirical values found by comparing font size and bounding box
                // height. Applies to the default font family.
                // http://jsfiddle.net/highcharts/7xvn7/
                lineHeight = fontSize < 24 ? fontSize + 3 : Math.round(fontSize * 1.2);
                baseline = Math.round(lineHeight * 0.8);

                return {
                    h: lineHeight,
                    b: baseline,
                    f: fontSize
                };
            },

            /**
             * Correct X and Y positioning of a label for rotation (#1764)
             */
            rotCorr: function(baseline, rotation, alterY) {
                var y = baseline;
                if (rotation && alterY) {
                    y = Math.max(y * Math.cos(rotation * deg2rad), 4);
                }
                return {
                    x: (-baseline / 3) * Math.sin(rotation * deg2rad),
                    y: y
                };
            },

            /**
             * Add a label, a text item that can hold a colored or gradient background
             * as well as a border and shadow. Supported custom attributes include
             * `padding`. 
             * 
             * @param {string} str
             * @param {number} x
             * @param {number} y
             * @param {String} shape
             * @param {number} anchorX In case the shape has a pointer, like a flag, this is the
             *	coordinates it should be pinned to
             * @param {number} anchorY
             * @param {Boolean} baseline Whether to position the label relative to the text baseline,
             *	like renderer.text, or to the upper border of the rectangle.
             * @param {String} className Class name for the group
             */
            label: function(str, x, y, shape, anchorX, anchorY, useHTML, baseline, className) {

                var renderer = this,
                    wrapper = renderer.g(className !== 'button' && 'label'),
                    text = wrapper.text = renderer.text('', 0, 0, useHTML)
                    .attr({
                        zIndex: 1
                    }),
                    box,
                    bBox,
                    alignFactor = 0,
                    padding = 3,
                    paddingLeft = 0,
                    width,
                    height,
                    wrapperX,
                    wrapperY,
                    textAlign,
                    deferredAttr = {},
                    strokeWidth,
                    baselineOffset,
                    hasBGImage = /^url\((.*?)\)$/.test(shape),
                    needsBox = hasBGImage,
                    getCrispAdjust,
                    updateBoxSize,
                    updateTextPadding,
                    boxAttr;

                if (className) {
                    wrapper.addClass('highcharts-' + className);
                }


                needsBox = hasBGImage;
                getCrispAdjust = function() {
                    return (strokeWidth || 0) % 2 / 2;
                };



                /**
                 * This function runs after the label is added to the DOM (when the bounding box is
                 * available), and after the text of the label is updated to detect the new bounding
                 * box and reflect it in the border box.
                 */
                updateBoxSize = function() {
                    var style = text.element.style,
                        crispAdjust,
                        attribs = {};

                    bBox = (width === undefined || height === undefined || textAlign) && defined(text.textStr) &&
                        text.getBBox(); //#3295 && 3514 box failure when string equals 0
                    wrapper.width = (width || bBox.width || 0) + 2 * padding + paddingLeft;
                    wrapper.height = (height || bBox.height || 0) + 2 * padding;

                    // Update the label-scoped y offset
                    baselineOffset = padding + renderer.fontMetrics(style && style.fontSize, text).b;


                    if (needsBox) {

                        // Create the border box if it is not already present
                        if (!box) {
                            wrapper.box = box = renderer.symbols[shape] || hasBGImage ? // Symbol definition exists (#5324)
                                renderer.symbol(shape) :
                                renderer.rect();

                            box.addClass(
                                (className === 'button' ? '' : 'highcharts-label-box') + // Don't use label className for buttons
                                (className ? ' highcharts-' + className + '-box' : '')
                            );

                            box.add(wrapper);

                            crispAdjust = getCrispAdjust();
                            attribs.x = crispAdjust;
                            attribs.y = (baseline ? -baselineOffset : 0) + crispAdjust;
                        }

                        // Apply the box attributes
                        attribs.width = Math.round(wrapper.width);
                        attribs.height = Math.round(wrapper.height);

                        box.attr(extend(attribs, deferredAttr));
                        deferredAttr = {};
                    }
                };

                /**
                 * This function runs after setting text or padding, but only if padding is changed
                 */
                updateTextPadding = function() {
                    var textX = paddingLeft + padding,
                        textY;

                    // determin y based on the baseline
                    textY = baseline ? 0 : baselineOffset;

                    // compensate for alignment
                    if (defined(width) && bBox && (textAlign === 'center' || textAlign === 'right')) {
                        textX += {
                            center: 0.5,
                            right: 1
                        }[textAlign] * (width - bBox.width);
                    }

                    // update if anything changed
                    if (textX !== text.x || textY !== text.y) {
                        text.attr('x', textX);
                        if (textY !== undefined) {
                            text.attr('y', textY);
                        }
                    }

                    // record current values
                    text.x = textX;
                    text.y = textY;
                };

                /**
                 * Set a box attribute, or defer it if the box is not yet created
                 * @param {Object} key
                 * @param {Object} value
                 */
                boxAttr = function(key, value) {
                    if (box) {
                        box.attr(key, value);
                    } else {
                        deferredAttr[key] = value;
                    }
                };

                /**
                 * After the text element is added, get the desired size of the border box
                 * and add it before the text in the DOM.
                 */
                wrapper.onAdd = function() {
                    text.add(wrapper);
                    wrapper.attr({
                        text: (str || str === 0) ? str : '', // alignment is available now // #3295: 0 not rendered if given as a value
                        x: x,
                        y: y
                    });

                    if (box && defined(anchorX)) {
                        wrapper.attr({
                            anchorX: anchorX,
                            anchorY: anchorY
                        });
                    }
                };

                /*
                 * Add specific attribute setters.
                 */

                // only change local variables
                wrapper.widthSetter = function(value) {
                    width = value;
                };
                wrapper.heightSetter = function(value) {
                    height = value;
                };
                wrapper['text-alignSetter'] = function(value) {
                    textAlign = value;
                };
                wrapper.paddingSetter = function(value) {
                    if (defined(value) && value !== padding) {
                        padding = wrapper.padding = value;
                        updateTextPadding();
                    }
                };
                wrapper.paddingLeftSetter = function(value) {
                    if (defined(value) && value !== paddingLeft) {
                        paddingLeft = value;
                        updateTextPadding();
                    }
                };


                // change local variable and prevent setting attribute on the group
                wrapper.alignSetter = function(value) {
                    value = {
                        left: 0,
                        center: 0.5,
                        right: 1
                    }[value];
                    if (value !== alignFactor) {
                        alignFactor = value;
                        if (bBox) { // Bounding box exists, means we're dynamically changing
                            wrapper.attr({
                                x: wrapperX
                            }); // #5134
                        }
                    }
                };

                // apply these to the box and the text alike
                wrapper.textSetter = function(value) {
                    if (value !== undefined) {
                        text.textSetter(value);
                    }
                    updateBoxSize();
                    updateTextPadding();
                };

                // apply these to the box but not to the text
                wrapper['stroke-widthSetter'] = function(value, key) {
                    if (value) {
                        needsBox = true;
                    }
                    strokeWidth = this['stroke-width'] = value;
                    boxAttr(key, value);
                };

                wrapper.strokeSetter = wrapper.fillSetter = wrapper.rSetter = function(value, key) {
                    if (key === 'fill' && value) {
                        needsBox = true;
                    }
                    boxAttr(key, value);
                };

                wrapper.anchorXSetter = function(value, key) {
                    anchorX = value;
                    boxAttr(key, Math.round(value) - getCrispAdjust() - wrapperX);
                };
                wrapper.anchorYSetter = function(value, key) {
                    anchorY = value;
                    boxAttr(key, value - wrapperY);
                };

                // rename attributes
                wrapper.xSetter = function(value) {
                    wrapper.x = value; // for animation getter
                    if (alignFactor) {
                        value -= alignFactor * ((width || bBox.width) + 2 * padding);
                    }
                    wrapperX = Math.round(value);
                    wrapper.attr('translateX', wrapperX);
                };
                wrapper.ySetter = function(value) {
                    wrapperY = wrapper.y = Math.round(value);
                    wrapper.attr('translateY', wrapperY);
                };

                // Redirect certain methods to either the box or the text
                var baseCss = wrapper.css;
                return extend(wrapper, {
                    /**
                     * Pick up some properties and apply them to the text instead of the
                     * wrapper.
                     * @ignore
                     */
                    css: function(styles) {
                        if (styles) {
                            var textStyles = {};
                            styles = merge(styles); // create a copy to avoid altering the original object (#537)
                            each(wrapper.textProps, function(prop) {
                                if (styles[prop] !== undefined) {
                                    textStyles[prop] = styles[prop];
                                    delete styles[prop];
                                }
                            });
                            text.css(textStyles);
                        }
                        return baseCss.call(wrapper, styles);
                    },
                    /**
                     * Return the bounding box of the box, not the group.
                     * @ignore
                     */
                    getBBox: function() {
                        return {
                            width: bBox.width + 2 * padding,
                            height: bBox.height + 2 * padding,
                            x: bBox.x - padding,
                            y: bBox.y - padding
                        };
                    },

                    /**
                     * Apply the shadow to the box.
                     * @ignore
                     */
                    shadow: function(b) {
                        if (b) {
                            updateBoxSize();
                            if (box) {
                                box.shadow(b);
                            }
                        }
                        return wrapper;
                    },

                    /**
                     * Destroy and release memory.
                     * @ignore
                     */
                    destroy: function() {

                        // Added by button implementation
                        removeEvent(wrapper.element, 'mouseenter');
                        removeEvent(wrapper.element, 'mouseleave');

                        if (text) {
                            text = text.destroy();
                        }
                        if (box) {
                            box = box.destroy();
                        }
                        // Call base implementation to destroy the rest
                        SVGElement.prototype.destroy.call(wrapper);

                        // Release local pointers (#1298)
                        wrapper = renderer = updateBoxSize = updateTextPadding = boxAttr = null;
                    }
                });
            }
        }; // end SVGRenderer


        // general renderer
        H.Renderer = SVGRenderer;

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var attr = H.attr,
            createElement = H.createElement,
            css = H.css,
            defined = H.defined,
            each = H.each,
            extend = H.extend,
            isFirefox = H.isFirefox,
            isMS = H.isMS,
            isWebKit = H.isWebKit,
            pInt = H.pInt,
            SVGElement = H.SVGElement,
            SVGRenderer = H.SVGRenderer,
            win = H.win,
            wrap = H.wrap;

        // Extend SvgElement for useHTML option
        extend(SVGElement.prototype, /** @lends SVGElement.prototype */ {
            /**
             * Apply CSS to HTML elements. This is used in text within SVG rendering and
             * by the VML renderer
             */
            htmlCss: function(styles) {
                var wrapper = this,
                    element = wrapper.element,
                    textWidth = styles && element.tagName === 'SPAN' && styles.width;

                if (textWidth) {
                    delete styles.width;
                    wrapper.textWidth = textWidth;
                    wrapper.updateTransform();
                }
                if (styles && styles.textOverflow === 'ellipsis') {
                    styles.whiteSpace = 'nowrap';
                    styles.overflow = 'hidden';
                }
                wrapper.styles = extend(wrapper.styles, styles);
                css(wrapper.element, styles);

                return wrapper;
            },

            /**
             * VML and useHTML method for calculating the bounding box based on offsets
             * @param {Boolean} refresh Whether to force a fresh value from the DOM or to
             * use the cached value
             *
             * @return {Object} A hash containing values for x, y, width and height
             */

            htmlGetBBox: function() {
                var wrapper = this,
                    element = wrapper.element;

                // faking getBBox in exported SVG in legacy IE
                // faking getBBox in exported SVG in legacy IE (is this a duplicate of the fix for #1079?)
                if (element.nodeName === 'text') {
                    element.style.position = 'absolute';
                }

                return {
                    x: element.offsetLeft,
                    y: element.offsetTop,
                    width: element.offsetWidth,
                    height: element.offsetHeight
                };
            },

            /**
             * VML override private method to update elements based on internal
             * properties based on SVG transform
             */
            htmlUpdateTransform: function() {
                // aligning non added elements is expensive
                if (!this.added) {
                    this.alignOnAdd = true;
                    return;
                }

                var wrapper = this,
                    renderer = wrapper.renderer,
                    elem = wrapper.element,
                    translateX = wrapper.translateX || 0,
                    translateY = wrapper.translateY || 0,
                    x = wrapper.x || 0,
                    y = wrapper.y || 0,
                    align = wrapper.textAlign || 'left',
                    alignCorrection = {
                        left: 0,
                        center: 0.5,
                        right: 1
                    }[align],
                    styles = wrapper.styles;

                // apply translate
                css(elem, {
                    marginLeft: translateX,
                    marginTop: translateY
                });


                if (wrapper.shadows) { // used in labels/tooltip
                    each(wrapper.shadows, function(shadow) {
                        css(shadow, {
                            marginLeft: translateX + 1,
                            marginTop: translateY + 1
                        });
                    });
                }


                // apply inversion
                if (wrapper.inverted) { // wrapper is a group
                    each(elem.childNodes, function(child) {
                        renderer.invertChild(child, elem);
                    });
                }

                if (elem.tagName === 'SPAN') {

                    var rotation = wrapper.rotation,
                        baseline,
                        textWidth = pInt(wrapper.textWidth),
                        whiteSpace = styles && styles.whiteSpace,
                        currentTextTransform = [rotation, align, elem.innerHTML, wrapper.textWidth, wrapper.textAlign].join(',');

                    if (currentTextTransform !== wrapper.cTT) { // do the calculations and DOM access only if properties changed


                        baseline = renderer.fontMetrics(elem.style.fontSize).b;

                        // Renderer specific handling of span rotation
                        if (defined(rotation)) {
                            wrapper.setSpanRotation(rotation, alignCorrection, baseline);
                        }

                        // Reset multiline/ellipsis in order to read width (#4928, #5417)
                        css(elem, {
                            width: '',
                            whiteSpace: whiteSpace || 'nowrap'
                        });

                        // Update textWidth
                        if (elem.offsetWidth > textWidth && /[ \-]/.test(elem.textContent || elem.innerText)) { // #983, #1254
                            css(elem, {
                                width: textWidth + 'px',
                                display: 'block',
                                whiteSpace: whiteSpace || 'normal' // #3331
                            });
                        }


                        wrapper.getSpanCorrection(elem.offsetWidth, baseline, alignCorrection, rotation, align);
                    }

                    // apply position with correction
                    css(elem, {
                        left: (x + (wrapper.xCorr || 0)) + 'px',
                        top: (y + (wrapper.yCorr || 0)) + 'px'
                    });

                    // force reflow in webkit to apply the left and top on useHTML element (#1249)
                    if (isWebKit) {
                        baseline = elem.offsetHeight; // assigned to baseline for lint purpose
                    }

                    // record current text transform
                    wrapper.cTT = currentTextTransform;
                }
            },

            /**
             * Set the rotation of an individual HTML span
             */
            setSpanRotation: function(rotation, alignCorrection, baseline) {
                var rotationStyle = {},
                    cssTransformKey = isMS ? '-ms-transform' : isWebKit ? '-webkit-transform' : isFirefox ? 'MozTransform' : win.opera ? '-o-transform' : '';

                rotationStyle[cssTransformKey] = rotationStyle.transform = 'rotate(' + rotation + 'deg)';
                rotationStyle[cssTransformKey + (isFirefox ? 'Origin' : '-origin')] = rotationStyle.transformOrigin = (alignCorrection * 100) + '% ' + baseline + 'px';
                css(this.element, rotationStyle);
            },

            /**
             * Get the correction in X and Y positioning as the element is rotated.
             */
            getSpanCorrection: function(width, baseline, alignCorrection) {
                this.xCorr = -width * alignCorrection;
                this.yCorr = -baseline;
            }
        });

        // Extend SvgRenderer for useHTML option.
        extend(SVGRenderer.prototype, /** @lends SVGRenderer.prototype */ {
            /**
             * Create HTML text node. This is used by the VML renderer as well as the SVG
             * renderer through the useHTML option.
             *
             * @param {String} str
             * @param {Number} x
             * @param {Number} y
             */
            html: function(str, x, y) {
                var wrapper = this.createElement('span'),
                    element = wrapper.element,
                    renderer = wrapper.renderer,
                    isSVG = renderer.isSVG,
                    addSetters = function(element, style) {
                        // These properties are set as attributes on the SVG group, and as
                        // identical CSS properties on the div. (#3542)
                        each(['opacity', 'visibility'], function(prop) {
                            wrap(element, prop + 'Setter', function(proceed, value, key, elem) {
                                proceed.call(this, value, key, elem);
                                style[key] = value;
                            });
                        });
                    };

                // Text setter
                wrapper.textSetter = function(value) {
                    if (value !== element.innerHTML) {
                        delete this.bBox;
                    }
                    element.innerHTML = this.textStr = value;
                    wrapper.htmlUpdateTransform();
                };

                // Add setters for the element itself (#4938)
                if (isSVG) { // #4938, only for HTML within SVG
                    addSetters(wrapper, wrapper.element.style);
                }

                // Various setters which rely on update transform
                wrapper.xSetter = wrapper.ySetter = wrapper.alignSetter = wrapper.rotationSetter = function(value, key) {
                    if (key === 'align') {
                        key = 'textAlign'; // Do not overwrite the SVGElement.align method. Same as VML.
                    }
                    wrapper[key] = value;
                    wrapper.htmlUpdateTransform();
                };

                // Set the default attributes
                wrapper
                    .attr({
                        text: str,
                        x: Math.round(x),
                        y: Math.round(y)
                    })
                    .css({

                        fontFamily: this.style.fontFamily,
                        fontSize: this.style.fontSize,

                        position: 'absolute'
                    });

                // Keep the whiteSpace style outside the wrapper.styles collection
                element.style.whiteSpace = 'nowrap';

                // Use the HTML specific .css method
                wrapper.css = wrapper.htmlCss;

                // This is specific for HTML within SVG
                if (isSVG) {
                    wrapper.add = function(svgGroupWrapper) {

                        var htmlGroup,
                            container = renderer.box.parentNode,
                            parentGroup,
                            parents = [];

                        this.parentGroup = svgGroupWrapper;

                        // Create a mock group to hold the HTML elements
                        if (svgGroupWrapper) {
                            htmlGroup = svgGroupWrapper.div;
                            if (!htmlGroup) {

                                // Read the parent chain into an array and read from top down
                                parentGroup = svgGroupWrapper;
                                while (parentGroup) {

                                    parents.push(parentGroup);

                                    // Move up to the next parent group
                                    parentGroup = parentGroup.parentGroup;
                                }

                                // Ensure dynamically updating position when any parent is translated
                                each(parents.reverse(), function(parentGroup) {
                                    var htmlGroupStyle,
                                        cls = attr(parentGroup.element, 'class');

                                    if (cls) {
                                        cls = {
                                            className: cls
                                        };
                                    } // else null

                                    // Create a HTML div and append it to the parent div to emulate
                                    // the SVG group structure
                                    htmlGroup = parentGroup.div = parentGroup.div || createElement('div', cls, {
                                        position: 'absolute',
                                        left: (parentGroup.translateX || 0) + 'px',
                                        top: (parentGroup.translateY || 0) + 'px',
                                        display: parentGroup.display,
                                        opacity: parentGroup.opacity, // #5075
                                        pointerEvents: parentGroup.styles && parentGroup.styles.pointerEvents // #5595
                                    }, htmlGroup || container); // the top group is appended to container

                                    // Shortcut
                                    htmlGroupStyle = htmlGroup.style;

                                    // Set listeners to update the HTML div's position whenever the SVG group
                                    // position is changed
                                    extend(parentGroup, {
                                        translateXSetter: function(value, key) {
                                            htmlGroupStyle.left = value + 'px';
                                            parentGroup[key] = value;
                                            parentGroup.doTransform = true;
                                        },
                                        translateYSetter: function(value, key) {
                                            htmlGroupStyle.top = value + 'px';
                                            parentGroup[key] = value;
                                            parentGroup.doTransform = true;
                                        }
                                    });
                                    addSetters(parentGroup, htmlGroupStyle);
                                });

                            }
                        } else {
                            htmlGroup = container;
                        }

                        htmlGroup.appendChild(element);

                        // Shared with VML:
                        wrapper.added = true;
                        if (wrapper.alignOnAdd) {
                            wrapper.htmlUpdateTransform();
                        }

                        return wrapper;
                    };
                }
                return wrapper;
            }
        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';

        var VMLRenderer,
            VMLRendererExtension,
            VMLElement,

            createElement = H.createElement,
            css = H.css,
            defined = H.defined,
            deg2rad = H.deg2rad,
            discardElement = H.discardElement,
            doc = H.doc,
            each = H.each,
            erase = H.erase,
            extend = H.extend,
            extendClass = H.extendClass,
            isArray = H.isArray,
            isNumber = H.isNumber,
            isObject = H.isObject,
            merge = H.merge,
            noop = H.noop,
            pick = H.pick,
            pInt = H.pInt,
            svg = H.svg,
            SVGElement = H.SVGElement,
            SVGRenderer = H.SVGRenderer,
            win = H.win;

        /* ****************************************************************************
         *                                                                            *
         * START OF INTERNET EXPLORER <= 8 SPECIFIC CODE                              *
         *                                                                            *
         * For applications and websites that don't need IE support, like platform    *
         * targeted mobile apps and web apps, this code can be removed.               *
         *                                                                            *
         *****************************************************************************/

        /**
         * @constructor
         */
        if (!svg) {

            /**
             * The VML element wrapper.
             */
            VMLElement = {

                docMode8: doc && doc.documentMode === 8,

                /**
                 * Initialize a new VML element wrapper. It builds the markup as a string
                 * to minimize DOM traffic.
                 * @param {Object} renderer
                 * @param {Object} nodeName
                 */
                init: function(renderer, nodeName) {
                    var wrapper = this,
                        markup = ['<', nodeName, ' filled="f" stroked="f"'],
                        style = ['position: ', 'absolute', ';'],
                        isDiv = nodeName === 'div';

                    // divs and shapes need size
                    if (nodeName === 'shape' || isDiv) {
                        style.push('left:0;top:0;width:1px;height:1px;');
                    }
                    style.push('visibility: ', isDiv ? 'hidden' : 'visible');

                    markup.push(' style="', style.join(''), '"/>');

                    // create element with default attributes and style
                    if (nodeName) {
                        markup = isDiv || nodeName === 'span' || nodeName === 'img' ?
                            markup.join('') :
                            renderer.prepVML(markup);
                        wrapper.element = createElement(markup);
                    }

                    wrapper.renderer = renderer;
                },

                /**
                 * Add the node to the given parent
                 * @param {Object} parent
                 */
                add: function(parent) {
                    var wrapper = this,
                        renderer = wrapper.renderer,
                        element = wrapper.element,
                        box = renderer.box,
                        inverted = parent && parent.inverted,

                        // get the parent node
                        parentNode = parent ?
                        parent.element || parent :
                        box;

                    if (parent) {
                        this.parentGroup = parent;
                    }

                    // if the parent group is inverted, apply inversion on all children
                    if (inverted) { // only on groups
                        renderer.invertChild(element, parentNode);
                    }

                    // append it
                    parentNode.appendChild(element);

                    // align text after adding to be able to read offset
                    wrapper.added = true;
                    if (wrapper.alignOnAdd && !wrapper.deferUpdateTransform) {
                        wrapper.updateTransform();
                    }

                    // fire an event for internal hooks
                    if (wrapper.onAdd) {
                        wrapper.onAdd();
                    }

                    // IE8 Standards can't set the class name before the element is appended
                    if (this.className) {
                        this.attr('class', this.className);
                    }

                    return wrapper;
                },

                /**
                 * VML always uses htmlUpdateTransform
                 */
                updateTransform: SVGElement.prototype.htmlUpdateTransform,

                /**
                 * Set the rotation of a span with oldIE's filter
                 */
                setSpanRotation: function() {
                    // Adjust for alignment and rotation. Rotation of useHTML content is not yet implemented
                    // but it can probably be implemented for Firefox 3.5+ on user request. FF3.5+
                    // has support for CSS3 transform. The getBBox method also needs to be updated
                    // to compensate for the rotation, like it currently does for SVG.
                    // Test case: http://jsfiddle.net/highcharts/Ybt44/

                    var rotation = this.rotation,
                        costheta = Math.cos(rotation * deg2rad),
                        sintheta = Math.sin(rotation * deg2rad);

                    css(this.element, {
                        filter: rotation ? ['progid:DXImageTransform.Microsoft.Matrix(M11=', costheta,
                            ', M12=', -sintheta, ', M21=', sintheta, ', M22=', costheta,
                            ', sizingMethod=\'auto expand\')'
                        ].join('') : 'none'
                    });
                },

                /**
                 * Get the positioning correction for the span after rotating.
                 */
                getSpanCorrection: function(width, baseline, alignCorrection, rotation, align) {

                    var costheta = rotation ? Math.cos(rotation * deg2rad) : 1,
                        sintheta = rotation ? Math.sin(rotation * deg2rad) : 0,
                        height = pick(this.elemHeight, this.element.offsetHeight),
                        quad,
                        nonLeft = align && align !== 'left';

                    // correct x and y
                    this.xCorr = costheta < 0 && -width;
                    this.yCorr = sintheta < 0 && -height;

                    // correct for baseline and corners spilling out after rotation
                    quad = costheta * sintheta < 0;
                    this.xCorr += sintheta * baseline * (quad ? 1 - alignCorrection : alignCorrection);
                    this.yCorr -= costheta * baseline * (rotation ? (quad ? alignCorrection : 1 - alignCorrection) : 1);
                    // correct for the length/height of the text
                    if (nonLeft) {
                        this.xCorr -= width * alignCorrection * (costheta < 0 ? -1 : 1);
                        if (rotation) {
                            this.yCorr -= height * alignCorrection * (sintheta < 0 ? -1 : 1);
                        }
                        css(this.element, {
                            textAlign: align
                        });
                    }
                },

                /**
                 * Converts a subset of an SVG path definition to its VML counterpart. Takes an array
                 * as the parameter and returns a string.
                 */
                pathToVML: function(value) {
                    // convert paths
                    var i = value.length,
                        path = [];

                    while (i--) {

                        // Multiply by 10 to allow subpixel precision.
                        // Substracting half a pixel seems to make the coordinates
                        // align with SVG, but this hasn't been tested thoroughly
                        if (isNumber(value[i])) {
                            path[i] = Math.round(value[i] * 10) - 5;
                        } else if (value[i] === 'Z') { // close the path
                            path[i] = 'x';
                        } else {
                            path[i] = value[i];

                            // When the start X and end X coordinates of an arc are too close,
                            // they are rounded to the same value above. In this case, substract or
                            // add 1 from the end X and Y positions. #186, #760, #1371, #1410.
                            if (value.isArc && (value[i] === 'wa' || value[i] === 'at')) {
                                // Start and end X
                                if (path[i + 5] === path[i + 7]) {
                                    path[i + 7] += value[i + 7] > value[i + 5] ? 1 : -1;
                                }
                                // Start and end Y
                                if (path[i + 6] === path[i + 8]) {
                                    path[i + 8] += value[i + 8] > value[i + 6] ? 1 : -1;
                                }
                            }
                        }
                    }


                    // Loop up again to handle path shortcuts (#2132)
                    /*while (i++ < path.length) {
                    	if (path[i] === 'H') { // horizontal line to
                    		path[i] = 'L';
                    		path.splice(i + 2, 0, path[i - 1]);
                    	} else if (path[i] === 'V') { // vertical line to
                    		path[i] = 'L';
                    		path.splice(i + 1, 0, path[i - 2]);
                    	}
                    }*/
                    return path.join(' ') || 'x';
                },

                /**
                 * Set the element's clipping to a predefined rectangle
                 *
                 * @param {String} id The id of the clip rectangle
                 */
                clip: function(clipRect) {
                    var wrapper = this,
                        clipMembers,
                        cssRet;

                    if (clipRect) {
                        clipMembers = clipRect.members;
                        erase(clipMembers, wrapper); // Ensure unique list of elements (#1258)
                        clipMembers.push(wrapper);
                        wrapper.destroyClip = function() {
                            erase(clipMembers, wrapper);
                        };
                        cssRet = clipRect.getCSS(wrapper);

                    } else {
                        if (wrapper.destroyClip) {
                            wrapper.destroyClip();
                        }
                        cssRet = {
                            clip: wrapper.docMode8 ? 'inherit' : 'rect(auto)'
                        }; // #1214
                    }

                    return wrapper.css(cssRet);

                },

                /**
                 * Set styles for the element
                 * @param {Object} styles
                 */
                css: SVGElement.prototype.htmlCss,

                /**
                 * Removes a child either by removeChild or move to garbageBin.
                 * Issue 490; in VML removeChild results in Orphaned nodes according to sIEve, discardElement does not.
                 */
                safeRemoveChild: function(element) {
                    // discardElement will detach the node from its parent before attaching it
                    // to the garbage bin. Therefore it is important that the node is attached and have parent.
                    if (element.parentNode) {
                        discardElement(element);
                    }
                },

                /**
                 * Extend element.destroy by removing it from the clip members array
                 */
                destroy: function() {
                    if (this.destroyClip) {
                        this.destroyClip();
                    }

                    return SVGElement.prototype.destroy.apply(this);
                },

                /**
                 * Add an event listener. VML override for normalizing event parameters.
                 * @param {String} eventType
                 * @param {Function} handler
                 */
                on: function(eventType, handler) {
                    // simplest possible event model for internal use
                    this.element['on' + eventType] = function() {
                        var evt = win.event;
                        evt.target = evt.srcElement;
                        handler(evt);
                    };
                    return this;
                },

                /**
                 * In stacked columns, cut off the shadows so that they don't overlap
                 */
                cutOffPath: function(path, length) {

                    var len;

                    path = path.split(/[ ,]/); // The extra comma tricks the trailing comma remover in "gulp scripts" task
                    len = path.length;

                    if (len === 9 || len === 11) {
                        path[len - 4] = path[len - 2] = pInt(path[len - 2]) - 10 * length;
                    }
                    return path.join(' ');
                },

                /**
                 * Apply a drop shadow by copying elements and giving them different strokes
                 * @param {Boolean|Object} shadowOptions
                 */
                shadow: function(shadowOptions, group, cutOff) {
                    var shadows = [],
                        i,
                        element = this.element,
                        renderer = this.renderer,
                        shadow,
                        elemStyle = element.style,
                        markup,
                        path = element.path,
                        strokeWidth,
                        modifiedPath,
                        shadowWidth,
                        shadowElementOpacity;

                    // some times empty paths are not strings
                    if (path && typeof path.value !== 'string') {
                        path = 'x';
                    }
                    modifiedPath = path;

                    if (shadowOptions) {
                        shadowWidth = pick(shadowOptions.width, 3);
                        shadowElementOpacity = (shadowOptions.opacity || 0.15) / shadowWidth;
                        for (i = 1; i <= 3; i++) {

                            strokeWidth = (shadowWidth * 2) + 1 - (2 * i);

                            // Cut off shadows for stacked column items
                            if (cutOff) {
                                modifiedPath = this.cutOffPath(path.value, strokeWidth + 0.5);
                            }

                            markup = ['<shape isShadow="true" strokeweight="', strokeWidth,
                                '" filled="false" path="', modifiedPath,
                                '" coordsize="10 10" style="', element.style.cssText, '" />'
                            ];

                            shadow = createElement(renderer.prepVML(markup),
                                null, {
                                    left: pInt(elemStyle.left) + pick(shadowOptions.offsetX, 1),
                                    top: pInt(elemStyle.top) + pick(shadowOptions.offsetY, 1)
                                }
                            );
                            if (cutOff) {
                                shadow.cutOff = strokeWidth + 1;
                            }

                            // apply the opacity
                            markup = [
                                '<stroke color="',
                                shadowOptions.color || '#000000',
                                '" opacity="', shadowElementOpacity * i, '"/>'
                            ];
                            createElement(renderer.prepVML(markup), null, null, shadow);


                            // insert it
                            if (group) {
                                group.element.appendChild(shadow);
                            } else {
                                element.parentNode.insertBefore(shadow, element);
                            }

                            // record it
                            shadows.push(shadow);

                        }

                        this.shadows = shadows;
                    }
                    return this;
                },
                updateShadows: noop, // Used in SVG only

                setAttr: function(key, value) {
                    if (this.docMode8) { // IE8 setAttribute bug
                        this.element[key] = value;
                    } else {
                        this.element.setAttribute(key, value);
                    }
                },
                classSetter: function(value) {
                    // IE8 Standards mode has problems retrieving the className unless set like this.
                    // IE8 Standards can't set the class name before the element is appended.
                    (this.added ? this.element : this).className = value;
                },
                dashstyleSetter: function(value, key, element) {
                    var strokeElem = element.getElementsByTagName('stroke')[0] ||
                        createElement(this.renderer.prepVML(['<stroke/>']), null, null, element);
                    strokeElem[key] = value || 'solid';
                    this[key] = value;
                    /* because changing stroke-width will change the dash length
				and cause an epileptic effect */
                },
                dSetter: function(value, key, element) {
                    var i,
                        shadows = this.shadows;
                    value = value || [];
                    this.d = value.join && value.join(' '); // used in getter for animation

                    element.path = value = this.pathToVML(value);

                    // update shadows
                    if (shadows) {
                        i = shadows.length;
                        while (i--) {
                            shadows[i].path = shadows[i].cutOff ? this.cutOffPath(value, shadows[i].cutOff) : value;
                        }
                    }
                    this.setAttr(key, value);
                },
                fillSetter: function(value, key, element) {
                    var nodeName = element.nodeName;
                    if (nodeName === 'SPAN') { // text color
                        element.style.color = value;
                    } else if (nodeName !== 'IMG') { // #1336
                        element.filled = value !== 'none';
                        this.setAttr('fillcolor', this.renderer.color(value, element, key, this));
                    }
                },
                'fill-opacitySetter': function(value, key, element) {
                    createElement(
                        this.renderer.prepVML(['<', key.split('-')[0], ' opacity="', value, '"/>']),
                        null,
                        null,
                        element
                    );
                },
                opacitySetter: noop, // Don't bother - animation is too slow and filters introduce artifacts
                rotationSetter: function(value, key, element) {
                    var style = element.style;
                    this[key] = style[key] = value; // style is for #1873

                    // Correction for the 1x1 size of the shape container. Used in gauge needles.
                    style.left = -Math.round(Math.sin(value * deg2rad) + 1) + 'px';
                    style.top = Math.round(Math.cos(value * deg2rad)) + 'px';
                },
                strokeSetter: function(value, key, element) {
                    this.setAttr('strokecolor', this.renderer.color(value, element, key, this));
                },
                'stroke-widthSetter': function(value, key, element) {
                    element.stroked = !!value; // VML "stroked" attribute
                    this[key] = value; // used in getter, issue #113
                    if (isNumber(value)) {
                        value += 'px';
                    }
                    this.setAttr('strokeweight', value);
                },
                titleSetter: function(value, key) {
                    this.setAttr(key, value);
                },
                visibilitySetter: function(value, key, element) {

                    // Handle inherited visibility
                    if (value === 'inherit') {
                        value = 'visible';
                    }

                    // Let the shadow follow the main element
                    if (this.shadows) {
                        each(this.shadows, function(shadow) {
                            shadow.style[key] = value;
                        });
                    }

                    // Instead of toggling the visibility CSS property, move the div out of the viewport.
                    // This works around #61 and #586
                    if (element.nodeName === 'DIV') {
                        value = value === 'hidden' ? '-999em' : 0;

                        // In order to redraw, IE7 needs the div to be visible when tucked away
                        // outside the viewport. So the visibility is actually opposite of
                        // the expected value. This applies to the tooltip only.
                        if (!this.docMode8) {
                            element.style[key] = value ? 'visible' : 'hidden';
                        }
                        key = 'top';
                    }
                    element.style[key] = value;
                },
                xSetter: function(value, key, element) {
                    this[key] = value; // used in getter

                    if (key === 'x') {
                        key = 'left';
                    } else if (key === 'y') {
                        key = 'top';
                    }
                    /* else {
                    				value = Math.max(0, value); // don't set width or height below zero (#311)
                    			}*/

                    // clipping rectangle special
                    if (this.updateClipping) {
                        this[key] = value; // the key is now 'left' or 'top' for 'x' and 'y'
                        this.updateClipping();
                    } else {
                        // normal
                        element.style[key] = value;
                    }
                },
                zIndexSetter: function(value, key, element) {
                    element.style[key] = value;
                }
            };
            VMLElement['stroke-opacitySetter'] = VMLElement['fill-opacitySetter'];
            H.VMLElement = VMLElement = extendClass(SVGElement, VMLElement);

            // Some shared setters
            VMLElement.prototype.ySetter =
                VMLElement.prototype.widthSetter =
                VMLElement.prototype.heightSetter =
                VMLElement.prototype.xSetter;


            /**
             * The VML renderer
             */
            VMLRendererExtension = { // inherit SVGRenderer

                Element: VMLElement,
                isIE8: win.navigator.userAgent.indexOf('MSIE 8.0') > -1,


                /**
                 * Initialize the VMLRenderer
                 * @param {Object} container
                 * @param {Number} width
                 * @param {Number} height
                 */
                init: function(container, width, height) {
                    var renderer = this,
                        boxWrapper,
                        box,
                        css;

                    renderer.alignedObjects = [];

                    boxWrapper = renderer.createElement('div')
                        .css({
                            position: 'relative'
                        });
                    box = boxWrapper.element;
                    container.appendChild(boxWrapper.element);


                    // generate the containing box
                    renderer.isVML = true;
                    renderer.box = box;
                    renderer.boxWrapper = boxWrapper;
                    renderer.gradients = {};
                    renderer.cache = {}; // Cache for numerical bounding boxes
                    renderer.cacheKeys = [];
                    renderer.imgCount = 0;


                    renderer.setSize(width, height, false);

                    // The only way to make IE6 and IE7 print is to use a global namespace. However,
                    // with IE8 the only way to make the dynamic shapes visible in screen and print mode
                    // seems to be to add the xmlns attribute and the behaviour style inline.
                    if (!doc.namespaces.hcv) {

                        doc.namespaces.add('hcv', 'urn:schemas-microsoft-com:vml');

                        // Setup default CSS (#2153, #2368, #2384)
                        css = 'hcv\\:fill, hcv\\:path, hcv\\:shape, hcv\\:stroke' +
                            '{ behavior:url(#default#VML); display: inline-block; } ';
                        try {
                            doc.createStyleSheet().cssText = css;
                        } catch (e) {
                            doc.styleSheets[0].cssText += css;
                        }

                    }
                },


                /**
                 * Detect whether the renderer is hidden. This happens when one of the parent elements
                 * has display: none
                 */
                isHidden: function() {
                    return !this.box.offsetWidth;
                },

                /**
                 * Define a clipping rectangle. In VML it is accomplished by storing the values
                 * for setting the CSS style to all associated members.
                 *
                 * @param {Number} x
                 * @param {Number} y
                 * @param {Number} width
                 * @param {Number} height
                 */
                clipRect: function(x, y, width, height) {

                    // create a dummy element
                    var clipRect = this.createElement(),
                        isObj = isObject(x);

                    // mimic a rectangle with its style object for automatic updating in attr
                    return extend(clipRect, {
                        members: [],
                        count: 0,
                        left: (isObj ? x.x : x) + 1,
                        top: (isObj ? x.y : y) + 1,
                        width: (isObj ? x.width : width) - 1,
                        height: (isObj ? x.height : height) - 1,
                        getCSS: function(wrapper) {
                            var element = wrapper.element,
                                nodeName = element.nodeName,
                                isShape = nodeName === 'shape',
                                inverted = wrapper.inverted,
                                rect = this,
                                top = rect.top - (isShape ? element.offsetTop : 0),
                                left = rect.left,
                                right = left + rect.width,
                                bottom = top + rect.height,
                                ret = {
                                    clip: 'rect(' +
                                        Math.round(inverted ? left : top) + 'px,' +
                                        Math.round(inverted ? bottom : right) + 'px,' +
                                        Math.round(inverted ? right : bottom) + 'px,' +
                                        Math.round(inverted ? top : left) + 'px)'
                                };

                            // issue 74 workaround
                            if (!inverted && wrapper.docMode8 && nodeName === 'DIV') {
                                extend(ret, {
                                    width: right + 'px',
                                    height: bottom + 'px'
                                });
                            }
                            return ret;
                        },

                        // used in attr and animation to update the clipping of all members
                        updateClipping: function() {
                            each(clipRect.members, function(member) {
                                // Member.element is falsy on deleted series, like in
                                // stock/members/series-remove demo. Should be removed
                                // from members, but this will do.
                                if (member.element) {
                                    member.css(clipRect.getCSS(member));
                                }
                            });
                        }
                    });

                },


                /**
                 * Take a color and return it if it's a string, make it a gradient if it's a
                 * gradient configuration object, and apply opacity.
                 *
                 * @param {Object} color The color or config object
                 */
                color: function(color, elem, prop, wrapper) {
                    var renderer = this,
                        colorObject,
                        regexRgba = /^rgba/,
                        markup,
                        fillType,
                        ret = 'none';

                    // Check for linear or radial gradient
                    if (color && color.linearGradient) {
                        fillType = 'gradient';
                    } else if (color && color.radialGradient) {
                        fillType = 'pattern';
                    }


                    if (fillType) {

                        var stopColor,
                            stopOpacity,
                            gradient = color.linearGradient || color.radialGradient,
                            x1,
                            y1,
                            x2,
                            y2,
                            opacity1,
                            opacity2,
                            color1,
                            color2,
                            fillAttr = '',
                            stops = color.stops,
                            firstStop,
                            lastStop,
                            colors = [],
                            addFillNode = function() {
                                // Add the fill subnode. When colors attribute is used, the meanings of opacity and o:opacity2
                                // are reversed.
                                markup = ['<fill colors="' + colors.join(',') +
                                    '" opacity="', opacity2, '" o:opacity2="',
                                    opacity1, '" type="', fillType, '" ', fillAttr,
                                    'focus="100%" method="any" />'
                                ];
                                createElement(renderer.prepVML(markup), null, null, elem);
                            };

                        // Extend from 0 to 1
                        firstStop = stops[0];
                        lastStop = stops[stops.length - 1];
                        if (firstStop[0] > 0) {
                            stops.unshift([
                                0,
                                firstStop[1]
                            ]);
                        }
                        if (lastStop[0] < 1) {
                            stops.push([
                                1,
                                lastStop[1]
                            ]);
                        }

                        // Compute the stops
                        each(stops, function(stop, i) {
                            if (regexRgba.test(stop[1])) {
                                colorObject = H.color(stop[1]);
                                stopColor = colorObject.get('rgb');
                                stopOpacity = colorObject.get('a');
                            } else {
                                stopColor = stop[1];
                                stopOpacity = 1;
                            }

                            // Build the color attribute
                            colors.push((stop[0] * 100) + '% ' + stopColor);

                            // Only start and end opacities are allowed, so we use the first and the last
                            if (!i) {
                                opacity1 = stopOpacity;
                                color2 = stopColor;
                            } else {
                                opacity2 = stopOpacity;
                                color1 = stopColor;
                            }
                        });

                        // Apply the gradient to fills only.
                        if (prop === 'fill') {

                            // Handle linear gradient angle
                            if (fillType === 'gradient') {
                                x1 = gradient.x1 || gradient[0] || 0;
                                y1 = gradient.y1 || gradient[1] || 0;
                                x2 = gradient.x2 || gradient[2] || 0;
                                y2 = gradient.y2 || gradient[3] || 0;
                                fillAttr = 'angle="' + (90 - Math.atan(
                                    (y2 - y1) / // y vector
                                    (x2 - x1) // x vector
                                ) * 180 / Math.PI) + '"';

                                addFillNode();

                                // Radial (circular) gradient
                            } else {

                                var r = gradient.r,
                                    sizex = r * 2,
                                    sizey = r * 2,
                                    cx = gradient.cx,
                                    cy = gradient.cy,
                                    radialReference = elem.radialReference,
                                    bBox,
                                    applyRadialGradient = function() {
                                        if (radialReference) {
                                            bBox = wrapper.getBBox();
                                            cx += (radialReference[0] - bBox.x) / bBox.width - 0.5;
                                            cy += (radialReference[1] - bBox.y) / bBox.height - 0.5;
                                            sizex *= radialReference[2] / bBox.width;
                                            sizey *= radialReference[2] / bBox.height;
                                        }
                                        fillAttr = 'src="' + H.getOptions().global.VMLRadialGradientURL + '" ' +
                                            'size="' + sizex + ',' + sizey + '" ' +
                                            'origin="0.5,0.5" ' +
                                            'position="' + cx + ',' + cy + '" ' +
                                            'color2="' + color2 + '" ';

                                        addFillNode();
                                    };

                                // Apply radial gradient
                                if (wrapper.added) {
                                    applyRadialGradient();
                                } else {
                                    // We need to know the bounding box to get the size and position right
                                    wrapper.onAdd = applyRadialGradient;
                                }

                                // The fill element's color attribute is broken in IE8 standards mode, so we
                                // need to set the parent shape's fillcolor attribute instead.
                                ret = color1;
                            }

                            // Gradients are not supported for VML stroke, return the first color. #722.
                        } else {
                            ret = stopColor;
                        }

                        // If the color is an rgba color, split it and add a fill node
                        // to hold the opacity component
                    } else if (regexRgba.test(color) && elem.tagName !== 'IMG') {

                        colorObject = H.color(color);

                        wrapper[prop + '-opacitySetter'](colorObject.get('a'), prop, elem);

                        ret = colorObject.get('rgb');


                    } else {
                        var propNodes = elem.getElementsByTagName(prop); // 'stroke' or 'fill' node
                        if (propNodes.length) {
                            propNodes[0].opacity = 1;
                            propNodes[0].type = 'solid';
                        }
                        ret = color;
                    }

                    return ret;
                },

                /**
                 * Take a VML string and prepare it for either IE8 or IE6/IE7.
                 * @param {Array} markup A string array of the VML markup to prepare
                 */
                prepVML: function(markup) {
                    var vmlStyle = 'display:inline-block;behavior:url(#default#VML);',
                        isIE8 = this.isIE8;

                    markup = markup.join('');

                    if (isIE8) { // add xmlns and style inline
                        markup = markup.replace('/>', ' xmlns="urn:schemas-microsoft-com:vml" />');
                        if (markup.indexOf('style="') === -1) {
                            markup = markup.replace('/>', ' style="' + vmlStyle + '" />');
                        } else {
                            markup = markup.replace('style="', 'style="' + vmlStyle);
                        }

                    } else { // add namespace
                        markup = markup.replace('<', '<hcv:');
                    }

                    return markup;
                },

                /**
                 * Create rotated and aligned text
                 * @param {String} str
                 * @param {Number} x
                 * @param {Number} y
                 */
                text: SVGRenderer.prototype.html,

                /**
                 * Create and return a path element
                 * @param {Array} path
                 */
                path: function(path) {
                    var attr = {
                        // subpixel precision down to 0.1 (width and height = 1px)
                        coordsize: '10 10'
                    };
                    if (isArray(path)) {
                        attr.d = path;
                    } else if (isObject(path)) { // attributes
                        extend(attr, path);
                    }
                    // create the shape
                    return this.createElement('shape').attr(attr);
                },

                /**
                 * Create and return a circle element. In VML circles are implemented as
                 * shapes, which is faster than v:oval
                 * @param {Number} x
                 * @param {Number} y
                 * @param {Number} r
                 */
                circle: function(x, y, r) {
                    var circle = this.symbol('circle');
                    if (isObject(x)) {
                        r = x.r;
                        y = x.y;
                        x = x.x;
                    }
                    circle.isCircle = true; // Causes x and y to mean center (#1682)
                    circle.r = r;
                    return circle.attr({
                        x: x,
                        y: y
                    });
                },

                /**
                 * Create a group using an outer div and an inner v:group to allow rotating
                 * and flipping. A simple v:group would have problems with positioning
                 * child HTML elements and CSS clip.
                 *
                 * @param {String} name The name of the group
                 */
                g: function(name) {
                    var wrapper,
                        attribs;

                    // set the class name
                    if (name) {
                        attribs = {
                            'className': 'highcharts-' + name,
                            'class': 'highcharts-' + name
                        };
                    }

                    // the div to hold HTML and clipping
                    wrapper = this.createElement('div').attr(attribs);

                    return wrapper;
                },

                /**
                 * VML override to create a regular HTML image
                 * @param {String} src
                 * @param {Number} x
                 * @param {Number} y
                 * @param {Number} width
                 * @param {Number} height
                 */
                image: function(src, x, y, width, height) {
                    var obj = this.createElement('img')
                        .attr({
                            src: src
                        });

                    if (arguments.length > 1) {
                        obj.attr({
                            x: x,
                            y: y,
                            width: width,
                            height: height
                        });
                    }
                    return obj;
                },

                /**
                 * For rectangles, VML uses a shape for rect to overcome bugs and rotation problems
                 */
                createElement: function(nodeName) {
                    return nodeName === 'rect' ?
                        this.symbol(nodeName) :
                        SVGRenderer.prototype.createElement.call(this, nodeName);
                },

                /**
                 * In the VML renderer, each child of an inverted div (group) is inverted
                 * @param {Object} element
                 * @param {Object} parentNode
                 */
                invertChild: function(element, parentNode) {
                    var ren = this,
                        parentStyle = parentNode.style,
                        imgStyle = element.tagName === 'IMG' && element.style; // #1111

                    css(element, {
                        flip: 'x',
                        left: pInt(parentStyle.width) - (imgStyle ? pInt(imgStyle.top) : 1),
                        top: pInt(parentStyle.height) - (imgStyle ? pInt(imgStyle.left) : 1),
                        rotation: -90
                    });

                    // Recursively invert child elements, needed for nested composite
                    // shapes like box plots and error bars. #1680, #1806.
                    each(element.childNodes, function(child) {
                        ren.invertChild(child, element);
                    });
                },

                /**
                 * Symbol definitions that override the parent SVG renderer's symbols
                 *
                 */
                symbols: {
                    // VML specific arc function
                    arc: function(x, y, w, h, options) {
                        var start = options.start,
                            end = options.end,
                            radius = options.r || w || h,
                            innerRadius = options.innerR,
                            cosStart = Math.cos(start),
                            sinStart = Math.sin(start),
                            cosEnd = Math.cos(end),
                            sinEnd = Math.sin(end),
                            ret;

                        if (end - start === 0) { // no angle, don't show it.
                            return ['x'];
                        }

                        ret = [
                            'wa', // clockwise arc to
                            x - radius, // left
                            y - radius, // top
                            x + radius, // right
                            y + radius, // bottom
                            x + radius * cosStart, // start x
                            y + radius * sinStart, // start y
                            x + radius * cosEnd, // end x
                            y + radius * sinEnd // end y
                        ];

                        if (options.open && !innerRadius) {
                            ret.push(
                                'e',
                                'M',
                                x, // - innerRadius,
                                y // - innerRadius
                            );
                        }

                        ret.push(
                            'at', // anti clockwise arc to
                            x - innerRadius, // left
                            y - innerRadius, // top
                            x + innerRadius, // right
                            y + innerRadius, // bottom
                            x + innerRadius * cosEnd, // start x
                            y + innerRadius * sinEnd, // start y
                            x + innerRadius * cosStart, // end x
                            y + innerRadius * sinStart, // end y
                            'x', // finish path
                            'e' // close
                        );

                        ret.isArc = true;
                        return ret;

                    },
                    // Add circle symbol path. This performs significantly faster than v:oval.
                    circle: function(x, y, w, h, wrapper) {

                        if (wrapper && defined(wrapper.r)) {
                            w = h = 2 * wrapper.r;
                        }

                        // Center correction, #1682
                        if (wrapper && wrapper.isCircle) {
                            x -= w / 2;
                            y -= h / 2;
                        }

                        // Return the path
                        return [
                            'wa', // clockwisearcto
                            x, // left
                            y, // top
                            x + w, // right
                            y + h, // bottom
                            x + w, // start x
                            y + h / 2, // start y
                            x + w, // end x
                            y + h / 2, // end y
                            //'x', // finish path
                            'e' // close
                        ];
                    },
                    /**
                     * Add rectangle symbol path which eases rotation and omits arcsize problems
                     * compared to the built-in VML roundrect shape. When borders are not rounded,
                     * use the simpler square path, else use the callout path without the arrow.
                     */
                    rect: function(x, y, w, h, options) {
                        return SVGRenderer.prototype.symbols[!defined(options) || !options.r ? 'square' : 'callout'].call(0, x, y, w, h, options);
                    }
                }
            };
            H.VMLRenderer = VMLRenderer = function() {
                this.init.apply(this, arguments);
            };
            VMLRenderer.prototype = merge(SVGRenderer.prototype, VMLRendererExtension);

            // general renderer
            H.Renderer = VMLRenderer;
        }

        // This method is used with exporting in old IE, when emulating SVG (see #2314)
        SVGRenderer.prototype.measureSpanWidth = function(text, styles) {
            var measuringSpan = doc.createElement('span'),
                offsetWidth,
                textNode = doc.createTextNode(text);

            measuringSpan.appendChild(textNode);
            css(measuringSpan, styles);
            this.box.appendChild(measuringSpan);
            offsetWidth = measuringSpan.offsetWidth;
            discardElement(measuringSpan); // #2463
            return offsetWidth;
        };


        /* ****************************************************************************
         *                                                                            *
         * END OF INTERNET EXPLORER <= 8 SPECIFIC CODE                                *
         *                                                                            *
         *****************************************************************************/


    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var color = H.color,
            each = H.each,
            getTZOffset = H.getTZOffset,
            isTouchDevice = H.isTouchDevice,
            merge = H.merge,
            pick = H.pick,
            svg = H.svg,
            win = H.win;

        /* ****************************************************************************
         * Handle the options                                                         *
         *****************************************************************************/
        H.defaultOptions = {

            colors: '#7cb5ec #434348 #90ed7d #f7a35c #8085e9 #f15c80 #e4d354 #2b908f #f45b5b #91e8e1'.split(' '),

            symbols: ['circle', 'diamond', 'square', 'triangle', 'triangle-down'],
            lang: {
                loading: 'Loading...',
                months: ['January', 'February', 'March', 'April', 'May', 'June', 'July',
                    'August', 'September', 'October', 'November', 'December'
                ],
                shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                // invalidDate: '',
                decimalPoint: '.',
                numericSymbols: ['k', 'M', 'G', 'T', 'P', 'E'], // SI prefixes used in axis labels
                resetZoom: 'Reset zoom',
                resetZoomTitle: 'Reset zoom level 1:1',
                thousandsSep: ' '
            },
            global: {
                useUTC: true,
                //timezoneOffset: 0,

                VMLRadialGradientURL: 'http://code.highcharts.com/5.0.4/gfx/vml-radial-gradient.png'

            },
            chart: {
                //animation: true,
                //alignTicks: false,
                //reflow: true,
                //className: null,
                //events: { load, selection },
                //margin: [null],
                //marginTop: null,
                //marginRight: null,
                //marginBottom: null,
                //marginLeft: null,
                borderRadius: 0,

                defaultSeriesType: 'line',
                ignoreHiddenSeries: true,
                //inverted: false,
                spacing: [10, 10, 15, 10],
                //spacingTop: 10,
                //spacingRight: 10,
                //spacingBottom: 15,
                //spacingLeft: 10,
                //zoomType: ''
                resetZoomButton: {
                    theme: {
                        zIndex: 20
                    },
                    position: {
                        align: 'right',
                        x: -10,
                        //verticalAlign: 'top',
                        y: 10
                    }
                    // relativeTo: 'plot'
                },
                width: null,
                height: null,


                borderColor: '#335cad',
                //borderWidth: 0,
                //style: {
                //	fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Verdana, Arial, Helvetica, sans-serif', // default font
                //	fontSize: '12px'
                //},
                backgroundColor: '#ffffff',
                //plotBackgroundColor: null,
                plotBorderColor: '#cccccc'
                    //plotBorderWidth: 0,
                    //plotShadow: false

            },
            title: {
                text: 'Chart title',
                align: 'center',
                // floating: false,
                margin: 15,
                // x: 0,
                // verticalAlign: 'top',
                // y: null,

                style: {
                    color: '#333333',
                    fontSize: '18px'
                },

                widthAdjust: -44

            },
            subtitle: {
                text: '',
                align: 'center',
                // floating: false
                // x: 0,
                // verticalAlign: 'top',
                // y: null,

                style: {
                    color: '#666666'
                },

                widthAdjust: -44
            },

            plotOptions: {},
            labels: {
                //items: [],
                style: {
                    //font: defaultFont,
                    position: 'absolute',
                    color: '#333333'
                }
            },
            legend: {
                enabled: true,
                align: 'center',
                //floating: false,
                layout: 'horizontal',
                labelFormatter: function() {
                    return this.name;
                },
                //borderWidth: 0,
                borderColor: '#999999',
                borderRadius: 0,
                navigation: {

                    activeColor: '#003399',
                    inactiveColor: '#cccccc'

                    // animation: true,
                    // arrowSize: 12
                    // style: {} // text styles
                },
                // margin: 20,
                // reversed: false,
                // backgroundColor: null,
                /*style: {
                	padding: '5px'
                },*/

                itemStyle: {
                    color: '#333333',
                    fontSize: '12px',
                    fontWeight: 'bold'
                },
                itemHoverStyle: {
                    //cursor: 'pointer', removed as of #601
                    color: '#000000'
                },
                itemHiddenStyle: {
                    color: '#cccccc'
                },
                shadow: false,

                itemCheckboxStyle: {
                    position: 'absolute',
                    width: '13px', // for IE precision
                    height: '13px'
                },
                // itemWidth: undefined,
                squareSymbol: true,
                // symbolRadius: 0,
                // symbolWidth: 16,
                symbolPadding: 5,
                verticalAlign: 'bottom',
                // width: undefined,
                x: 0,
                y: 0,
                title: {
                    //text: null,

                    style: {
                        fontWeight: 'bold'
                    }

                }
            },

            loading: {
                // hideDuration: 100,
                // showDuration: 0,

                labelStyle: {
                    fontWeight: 'bold',
                    position: 'relative',
                    top: '45%'
                },
                style: {
                    position: 'absolute',
                    backgroundColor: '#ffffff',
                    opacity: 0.5,
                    textAlign: 'center'
                }

            },

            tooltip: {
                enabled: true,
                animation: svg,
                //crosshairs: null,
                borderRadius: 3,
                dateTimeLabelFormats: {
                    millisecond: '%A, %b %e, %H:%M:%S.%L',
                    second: '%A, %b %e, %H:%M:%S',
                    minute: '%A, %b %e, %H:%M',
                    hour: '%A, %b %e, %H:%M',
                    day: '%A, %b %e, %Y',
                    week: 'Week from %A, %b %e, %Y',
                    month: '%B %Y',
                    year: '%Y'
                },
                footerFormat: '',
                //formatter: defaultFormatter,
                /* todo: em font-size when finished comparing against HC4
                headerFormat: '<span style="font-size: 0.85em">{point.key}</span><br/>',
                */
                padding: 8,

                //shape: 'callout',
                //shared: false,
                snap: isTouchDevice ? 25 : 10,

                backgroundColor: color('#f7f7f7').setOpacity(0.85).get(),
                borderWidth: 1,
                headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
                pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}</b><br/>',
                shadow: true,
                style: {
                    color: '#333333',
                    cursor: 'default',
                    fontSize: '12px',
                    pointerEvents: 'none', // #1686 http://caniuse.com/#feat=pointer-events
                    whiteSpace: 'nowrap'
                }

                //xDateFormat: '%A, %b %e, %Y',
                //valueDecimals: null,
                //valuePrefix: '',
                //valueSuffix: ''
            },

            credits: {
                enabled: true,
                href: 'http://www.highcharts.com',
                position: {
                    align: 'right',
                    x: -10,
                    verticalAlign: 'bottom',
                    y: -5
                },

                style: {
                    cursor: 'pointer',
                    color: '#999999',
                    fontSize: '9px'
                },

                text: 'Highcharts.com'
            }
        };



        /**
         * Set the time methods globally based on the useUTC option. Time method can be
         *   either local time or UTC (default). It is called internally on initiating
         *   Highcharts and after running `Highcharts.setOptions`.
         *
         * @private
         */
        function setTimeMethods() {
            var globalOptions = H.defaultOptions.global,
                Date,
                useUTC = globalOptions.useUTC,
                GET = useUTC ? 'getUTC' : 'get',
                SET = useUTC ? 'setUTC' : 'set';

            H.Date = Date = globalOptions.Date || win.Date; // Allow using a different Date class
            Date.hcTimezoneOffset = useUTC && globalOptions.timezoneOffset;
            Date.hcGetTimezoneOffset = useUTC && globalOptions.getTimezoneOffset;
            Date.hcMakeTime = function(year, month, date, hours, minutes, seconds) {
                var d;
                if (useUTC) {
                    d = Date.UTC.apply(0, arguments);
                    d += getTZOffset(d);
                } else {
                    d = new Date(
                        year,
                        month,
                        pick(date, 1),
                        pick(hours, 0),
                        pick(minutes, 0),
                        pick(seconds, 0)
                    ).getTime();
                }
                return d;
            };
            each(['Minutes', 'Hours', 'Day', 'Date', 'Month', 'FullYear'], function(s) {
                Date['hcGet' + s] = GET + s;
            });
            each(['Milliseconds', 'Seconds', 'Minutes', 'Hours', 'Date', 'Month', 'FullYear'], function(s) {
                Date['hcSet' + s] = SET + s;
            });
        }

        /**
         * Merge the default options with custom options and return the new options structure
         * @param {Object} options The new custom options
         */
        H.setOptions = function(options) {

            // Copy in the default options
            H.defaultOptions = merge(true, H.defaultOptions, options);

            // Apply UTC
            setTimeMethods();

            return H.defaultOptions;
        };

        /**
         * Get the updated default options. Until 3.0.7, merely exposing defaultOptions for outside modules
         * wasn't enough because the setOptions method created a new object.
         */
        H.getOptions = function() {
            return H.defaultOptions;
        };


        // Series defaults
        H.defaultPlotOptions = H.defaultOptions.plotOptions;

        // set the default time methods
        setTimeMethods();

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var arrayMax = H.arrayMax,
            arrayMin = H.arrayMin,
            defined = H.defined,
            destroyObjectProperties = H.destroyObjectProperties,
            each = H.each,
            erase = H.erase,
            merge = H.merge,
            pick = H.pick;
        /*
         * The object wrapper for plot lines and plot bands
         * @param {Object} options
         */
        H.PlotLineOrBand = function(axis, options) {
            this.axis = axis;

            if (options) {
                this.options = options;
                this.id = options.id;
            }
        };

        H.PlotLineOrBand.prototype = {

            /**
             * Render the plot line or plot band. If it is already existing,
             * move it.
             */
            render: function() {
                var plotLine = this,
                    axis = plotLine.axis,
                    horiz = axis.horiz,
                    options = plotLine.options,
                    optionsLabel = options.label,
                    label = plotLine.label,
                    to = options.to,
                    from = options.from,
                    value = options.value,
                    isBand = defined(from) && defined(to),
                    isLine = defined(value),
                    svgElem = plotLine.svgElem,
                    isNew = !svgElem,
                    path = [],
                    addEvent,
                    eventType,
                    color = options.color,
                    zIndex = pick(options.zIndex, 0),
                    events = options.events,
                    attribs = {
                        'class': 'highcharts-plot-' + (isBand ? 'band ' : 'line ') + (options.className || '')
                    },
                    groupAttribs = {},
                    renderer = axis.chart.renderer,
                    groupName = isBand ? 'bands' : 'lines',
                    group,
                    log2lin = axis.log2lin;

                // logarithmic conversion
                if (axis.isLog) {
                    from = log2lin(from);
                    to = log2lin(to);
                    value = log2lin(value);
                }


                // Set the presentational attributes
                if (isLine) {
                    attribs = {
                        stroke: color,
                        'stroke-width': options.width
                    };
                    if (options.dashStyle) {
                        attribs.dashstyle = options.dashStyle;
                    }

                } else if (isBand) { // plot band
                    if (color) {
                        attribs.fill = color;
                    }
                    if (options.borderWidth) {
                        attribs.stroke = options.borderColor;
                        attribs['stroke-width'] = options.borderWidth;
                    }
                }


                // Grouping and zIndex
                groupAttribs.zIndex = zIndex;
                groupName += '-' + zIndex;

                group = axis[groupName];
                if (!group) {
                    axis[groupName] = group = renderer.g('plot-' + groupName)
                        .attr(groupAttribs).add();
                }

                // Create the path
                if (isNew) {
                    plotLine.svgElem = svgElem =
                        renderer
                        .path()
                        .attr(attribs).add(group);
                }


                // Set the path or return
                if (isLine) {
                    path = axis.getPlotLinePath(value, svgElem.strokeWidth());
                } else if (isBand) { // plot band
                    path = axis.getPlotBandPath(from, to, options);
                } else {
                    return;
                }

                // common for lines and bands
                if (isNew && path && path.length) {
                    svgElem.attr({
                        d: path
                    });

                    // events
                    if (events) {
                        addEvent = function(eventType) {
                            svgElem.on(eventType, function(e) {
                                events[eventType].apply(plotLine, [e]);
                            });
                        };
                        for (eventType in events) {
                            addEvent(eventType);
                        }
                    }
                } else if (svgElem) {
                    if (path) {
                        svgElem.show();
                        svgElem.animate({
                            d: path
                        });
                    } else {
                        svgElem.hide();
                        if (label) {
                            plotLine.label = label = label.destroy();
                        }
                    }
                }

                // the plot band/line label
                if (optionsLabel && defined(optionsLabel.text) && path && path.length &&
                    axis.width > 0 && axis.height > 0 && !path.flat) {
                    // apply defaults
                    optionsLabel = merge({
                        align: horiz && isBand && 'center',
                        x: horiz ? !isBand && 4 : 10,
                        verticalAlign: !horiz && isBand && 'middle',
                        y: horiz ? isBand ? 16 : 10 : isBand ? 6 : -4,
                        rotation: horiz && !isBand && 90
                    }, optionsLabel);

                    this.renderLabel(optionsLabel, path, isBand, zIndex);

                } else if (label) { // move out of sight
                    label.hide();
                }

                // chainable
                return plotLine;
            },

            /**
             * Render and align label for plot line or band.
             */
            renderLabel: function(optionsLabel, path, isBand, zIndex) {
                var plotLine = this,
                    label = plotLine.label,
                    renderer = plotLine.axis.chart.renderer,
                    attribs,
                    xs,
                    ys,
                    x,
                    y;

                // add the SVG element
                if (!label) {
                    attribs = {
                        align: optionsLabel.textAlign || optionsLabel.align,
                        rotation: optionsLabel.rotation,
                        'class': 'highcharts-plot-' + (isBand ? 'band' : 'line') + '-label ' + (optionsLabel.className || '')
                    };

                    attribs.zIndex = zIndex;

                    plotLine.label = label = renderer.text(
                            optionsLabel.text,
                            0,
                            0,
                            optionsLabel.useHTML
                        )
                        .attr(attribs)
                        .add();


                    label.css(optionsLabel.style);

                }

                // get the bounding box and align the label
                // #3000 changed to better handle choice between plotband or plotline
                xs = [path[1], path[4], (isBand ? path[6] : path[1])];
                ys = [path[2], path[5], (isBand ? path[7] : path[2])];
                x = arrayMin(xs);
                y = arrayMin(ys);

                label.align(optionsLabel, false, {
                    x: x,
                    y: y,
                    width: arrayMax(xs) - x,
                    height: arrayMax(ys) - y
                });
                label.show();
            },

            /**
             * Remove the plot line or band
             */
            destroy: function() {
                // remove it from the lookup
                erase(this.axis.plotLinesAndBands, this);

                delete this.axis;
                destroyObjectProperties(this);
            }
        };

        /**
         * Object with members for extending the Axis prototype
         * @todo Extend directly instead of adding object to Highcharts first
         */

        H.AxisPlotLineOrBandExtension = {

            /**
             * Create the path for a plot band
             */
            getPlotBandPath: function(from, to) {
                var toPath = this.getPlotLinePath(to, null, null, true),
                    path = this.getPlotLinePath(from, null, null, true);

                if (path && toPath) {

                    // Flat paths don't need labels (#3836)
                    path.flat = path.toString() === toPath.toString();

                    path.push(
                        toPath[4],
                        toPath[5],
                        toPath[1],
                        toPath[2],
                        'z' // #5909
                    );
                } else { // outside the axis area
                    path = null;
                }

                return path;
            },

            addPlotBand: function(options) {
                return this.addPlotBandOrLine(options, 'plotBands');
            },

            addPlotLine: function(options) {
                return this.addPlotBandOrLine(options, 'plotLines');
            },

            /**
             * Add a plot band or plot line after render time
             *
             * @param options {Object} The plotBand or plotLine configuration object
             */
            addPlotBandOrLine: function(options, coll) {
                var obj = new H.PlotLineOrBand(this, options).render(),
                    userOptions = this.userOptions;

                if (obj) { // #2189
                    // Add it to the user options for exporting and Axis.update
                    if (coll) {
                        userOptions[coll] = userOptions[coll] || [];
                        userOptions[coll].push(options);
                    }
                    this.plotLinesAndBands.push(obj);
                }

                return obj;
            },

            /**
             * Remove a plot band or plot line from the chart by id
             * @param {Object} id
             */
            removePlotBandOrLine: function(id) {
                var plotLinesAndBands = this.plotLinesAndBands,
                    options = this.options,
                    userOptions = this.userOptions,
                    i = plotLinesAndBands.length;
                while (i--) {
                    if (plotLinesAndBands[i].id === id) {
                        plotLinesAndBands[i].destroy();
                    }
                }
                each([options.plotLines || [], userOptions.plotLines || [], options.plotBands || [], userOptions.plotBands || []], function(arr) {
                    i = arr.length;
                    while (i--) {
                        if (arr[i].id === id) {
                            erase(arr, arr[i]);
                        }
                    }
                });
            }
        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var correctFloat = H.correctFloat,
            defined = H.defined,
            destroyObjectProperties = H.destroyObjectProperties,
            isNumber = H.isNumber,
            merge = H.merge,
            pick = H.pick,
            deg2rad = H.deg2rad;

        /**
         * The Tick class
         */
        H.Tick = function(axis, pos, type, noLabel) {
            this.axis = axis;
            this.pos = pos;
            this.type = type || '';
            this.isNew = true;

            if (!type && !noLabel) {
                this.addLabel();
            }
        };

        H.Tick.prototype = {
            /**
             * Write the tick label
             */
            addLabel: function() {
                var tick = this,
                    axis = tick.axis,
                    options = axis.options,
                    chart = axis.chart,
                    categories = axis.categories,
                    names = axis.names,
                    pos = tick.pos,
                    labelOptions = options.labels,
                    str,
                    tickPositions = axis.tickPositions,
                    isFirst = pos === tickPositions[0],
                    isLast = pos === tickPositions[tickPositions.length - 1],
                    value = categories ?
                    pick(categories[pos], names[pos], pos) :
                    pos,
                    label = tick.label,
                    tickPositionInfo = tickPositions.info,
                    dateTimeLabelFormat;

                // Set the datetime label format. If a higher rank is set for this position, use that. If not,
                // use the general format.
                if (axis.isDatetimeAxis && tickPositionInfo) {
                    dateTimeLabelFormat =
                        options.dateTimeLabelFormats[
                            tickPositionInfo.higherRanks[pos] || tickPositionInfo.unitName
                        ];
                }
                // set properties for access in render method
                tick.isFirst = isFirst;
                tick.isLast = isLast;

                // get the string
                str = axis.labelFormatter.call({
                    axis: axis,
                    chart: chart,
                    isFirst: isFirst,
                    isLast: isLast,
                    dateTimeLabelFormat: dateTimeLabelFormat,
                    value: axis.isLog ? correctFloat(axis.lin2log(value)) : value
                });

                // prepare CSS
                //css = width && { width: Math.max(1, Math.round(width - 2 * (labelOptions.padding || 10))) + 'px' };

                // first call
                if (!defined(label)) {

                    tick.label = label =
                        defined(str) && labelOptions.enabled ?
                        chart.renderer.text(
                            str,
                            0,
                            0,
                            labelOptions.useHTML
                        )

                    // without position absolute, IE export sometimes is wrong
                    .css(merge(labelOptions.style))

                    .add(axis.labelGroup):
                        null;
                    tick.labelLength = label && label.getBBox().width; // Un-rotated length
                    tick.rotation = 0; // Base value to detect change for new calls to getBBox

                    // update
                } else if (label) {
                    label.attr({
                        text: str
                    });
                }
            },

            /**
             * Get the offset height or width of the label
             */
            getLabelSize: function() {
                return this.label ?
                    this.label.getBBox()[this.axis.horiz ? 'height' : 'width'] :
                    0;
            },

            /**
             * Handle the label overflow by adjusting the labels to the left and right edge, or
             * hide them if they collide into the neighbour label.
             */
            handleOverflow: function(xy) {
                var axis = this.axis,
                    pxPos = xy.x,
                    chartWidth = axis.chart.chartWidth,
                    spacing = axis.chart.spacing,
                    leftBound = pick(axis.labelLeft, Math.min(axis.pos, spacing[3])),
                    rightBound = pick(axis.labelRight, Math.max(axis.pos + axis.len, chartWidth - spacing[1])),
                    label = this.label,
                    rotation = this.rotation,
                    factor = {
                        left: 0,
                        center: 0.5,
                        right: 1
                    }[axis.labelAlign],
                    labelWidth = label.getBBox().width,
                    slotWidth = axis.getSlotWidth(),
                    modifiedSlotWidth = slotWidth,
                    xCorrection = factor,
                    goRight = 1,
                    leftPos,
                    rightPos,
                    textWidth,
                    css = {};

                // Check if the label overshoots the chart spacing box. If it does, move it.
                // If it now overshoots the slotWidth, add ellipsis.
                if (!rotation) {
                    leftPos = pxPos - factor * labelWidth;
                    rightPos = pxPos + (1 - factor) * labelWidth;

                    if (leftPos < leftBound) {
                        modifiedSlotWidth = xy.x + modifiedSlotWidth * (1 - factor) - leftBound;
                    } else if (rightPos > rightBound) {
                        modifiedSlotWidth = rightBound - xy.x + modifiedSlotWidth * factor;
                        goRight = -1;
                    }

                    modifiedSlotWidth = Math.min(slotWidth, modifiedSlotWidth); // #4177
                    if (modifiedSlotWidth < slotWidth && axis.labelAlign === 'center') {
                        xy.x += goRight * (slotWidth - modifiedSlotWidth - xCorrection *
                            (slotWidth - Math.min(labelWidth, modifiedSlotWidth)));
                    }
                    // If the label width exceeds the available space, set a text width to be
                    // picked up below. Also, if a width has been set before, we need to set a new
                    // one because the reported labelWidth will be limited by the box (#3938).
                    if (labelWidth > modifiedSlotWidth || (axis.autoRotation && (label.styles || {}).width)) {
                        textWidth = modifiedSlotWidth;
                    }

                    // Add ellipsis to prevent rotated labels to be clipped against the edge of the chart
                } else if (rotation < 0 && pxPos - factor * labelWidth < leftBound) {
                    textWidth = Math.round(pxPos / Math.cos(rotation * deg2rad) - leftBound);
                } else if (rotation > 0 && pxPos + factor * labelWidth > rightBound) {
                    textWidth = Math.round((chartWidth - pxPos) / Math.cos(rotation * deg2rad));
                }

                if (textWidth) {
                    css.width = textWidth;
                    if (!(axis.options.labels.style || {}).textOverflow) {
                        css.textOverflow = 'ellipsis';
                    }
                    label.css(css);
                }
            },

            /**
             * Get the x and y position for ticks and labels
             */
            getPosition: function(horiz, pos, tickmarkOffset, old) {
                var axis = this.axis,
                    chart = axis.chart,
                    cHeight = (old && chart.oldChartHeight) || chart.chartHeight;

                return {
                    x: horiz ?
                        axis.translate(pos + tickmarkOffset, null, null, old) + axis.transB : axis.left + axis.offset +
                        (axis.opposite ?
                            ((old && chart.oldChartWidth) || chart.chartWidth) - axis.right - axis.left :
                            0
                        ),

                    y: horiz ?
                        cHeight - axis.bottom + axis.offset - (axis.opposite ? axis.height : 0) : cHeight - axis.translate(pos + tickmarkOffset, null, null, old) - axis.transB
                };

            },

            /**
             * Get the x, y position of the tick label
             */
            getLabelPosition: function(x, y, label, horiz, labelOptions, tickmarkOffset, index, step) {
                var axis = this.axis,
                    transA = axis.transA,
                    reversed = axis.reversed,
                    staggerLines = axis.staggerLines,
                    rotCorr = axis.tickRotCorr || {
                        x: 0,
                        y: 0
                    },
                    yOffset = labelOptions.y,
                    line;

                if (!defined(yOffset)) {
                    if (axis.side === 0) {
                        yOffset = label.rotation ? -8 : -label.getBBox().height;
                    } else if (axis.side === 2) {
                        yOffset = rotCorr.y + 8;
                    } else {
                        // #3140, #3140
                        yOffset = Math.cos(label.rotation * deg2rad) * (rotCorr.y - label.getBBox(false, 0).height / 2);
                    }
                }

                x = x + labelOptions.x + rotCorr.x - (tickmarkOffset && horiz ?
                    tickmarkOffset * transA * (reversed ? -1 : 1) : 0);
                y = y + yOffset - (tickmarkOffset && !horiz ?
                    tickmarkOffset * transA * (reversed ? 1 : -1) : 0);

                // Correct for staggered labels
                if (staggerLines) {
                    line = (index / (step || 1) % staggerLines);
                    if (axis.opposite) {
                        line = staggerLines - line - 1;
                    }
                    y += line * (axis.labelOffset / staggerLines);
                }

                return {
                    x: x,
                    y: Math.round(y)
                };
            },

            /**
             * Extendible method to return the path of the marker
             */
            getMarkPath: function(x, y, tickLength, tickWidth, horiz, renderer) {
                return renderer.crispLine([
                    'M',
                    x,
                    y,
                    'L',
                    x + (horiz ? 0 : -tickLength),
                    y + (horiz ? tickLength : 0)
                ], tickWidth);
            },

            /**
             * Put everything in place
             *
             * @param index {Number}
             * @param old {Boolean} Use old coordinates to prepare an animation into new position
             */
            render: function(index, old, opacity) {
                var tick = this,
                    axis = tick.axis,
                    options = axis.options,
                    chart = axis.chart,
                    renderer = chart.renderer,
                    horiz = axis.horiz,
                    type = tick.type,
                    label = tick.label,
                    pos = tick.pos,
                    labelOptions = options.labels,
                    gridLine = tick.gridLine,
                    tickPrefix = type ? type + 'Tick' : 'tick',
                    tickSize = axis.tickSize(tickPrefix),
                    gridLinePath,
                    mark = tick.mark,
                    isNewMark = !mark,
                    step = labelOptions.step,
                    attribs = {},
                    show = true,
                    tickmarkOffset = axis.tickmarkOffset,
                    xy = tick.getPosition(horiz, pos, tickmarkOffset, old),
                    x = xy.x,
                    y = xy.y,
                    reverseCrisp = ((horiz && x === axis.pos + axis.len) ||
                        (!horiz && y === axis.pos)) ? -1 : 1; // #1480, #1687


                var gridPrefix = type ? type + 'Grid' : 'grid',
                    gridLineWidth = options[gridPrefix + 'LineWidth'],
                    gridLineColor = options[gridPrefix + 'LineColor'],
                    dashStyle = options[gridPrefix + 'LineDashStyle'],
                    tickWidth = pick(options[tickPrefix + 'Width'], !type && axis.isXAxis ? 1 : 0), // X axis defaults to 1
                    tickColor = options[tickPrefix + 'Color'];


                opacity = pick(opacity, 1);
                this.isActive = true;

                // Create the grid line
                if (!gridLine) {

                    attribs.stroke = gridLineColor;
                    attribs['stroke-width'] = gridLineWidth;
                    if (dashStyle) {
                        attribs.dashstyle = dashStyle;
                    }

                    if (!type) {
                        attribs.zIndex = 1;
                    }
                    if (old) {
                        attribs.opacity = 0;
                    }
                    tick.gridLine = gridLine = renderer.path()
                        .attr(attribs)
                        .addClass('highcharts-' + (type ? type + '-' : '') + 'grid-line')
                        .add(axis.gridGroup);
                }

                // If the parameter 'old' is set, the current call will be followed
                // by another call, therefore do not do any animations this time
                if (!old && gridLine) {
                    gridLinePath = axis.getPlotLinePath(pos + tickmarkOffset, gridLine.strokeWidth() * reverseCrisp, old, true);
                    if (gridLinePath) {
                        gridLine[tick.isNew ? 'attr' : 'animate']({
                            d: gridLinePath,
                            opacity: opacity
                        });
                    }
                }

                // create the tick mark
                if (tickSize) {

                    // negate the length
                    if (axis.opposite) {
                        tickSize[0] = -tickSize[0];
                    }

                    // First time, create it
                    if (isNewMark) {
                        tick.mark = mark = renderer.path()
                            .addClass('highcharts-' + (type ? type + '-' : '') + 'tick')
                            .add(axis.axisGroup);


                        mark.attr({
                            stroke: tickColor,
                            'stroke-width': tickWidth
                        });

                    }
                    mark[isNewMark ? 'attr' : 'animate']({
                        d: tick.getMarkPath(x, y, tickSize[0], mark.strokeWidth() * reverseCrisp, horiz, renderer),
                        opacity: opacity
                    });

                }

                // the label is created on init - now move it into place
                if (label && isNumber(x)) {
                    label.xy = xy = tick.getLabelPosition(x, y, label, horiz, labelOptions, tickmarkOffset, index, step);

                    // Apply show first and show last. If the tick is both first and last, it is
                    // a single centered tick, in which case we show the label anyway (#2100).
                    if ((tick.isFirst && !tick.isLast && !pick(options.showFirstLabel, 1)) ||
                        (tick.isLast && !tick.isFirst && !pick(options.showLastLabel, 1))) {
                        show = false;

                        // Handle label overflow and show or hide accordingly
                    } else if (horiz && !axis.isRadial && !labelOptions.step &&
                        !labelOptions.rotation && !old && opacity !== 0) {
                        tick.handleOverflow(xy);
                    }

                    // apply step
                    if (step && index % step) {
                        // show those indices dividable by step
                        show = false;
                    }

                    // Set the new position, and show or hide
                    if (show && isNumber(xy.y)) {
                        xy.opacity = opacity;
                        label[tick.isNew ? 'attr' : 'animate'](xy);
                    } else {
                        label.attr('y', -9999); // #1338
                    }
                    tick.isNew = false;
                }
            },

            /**
             * Destructor for the tick prototype
             */
            destroy: function() {
                destroyObjectProperties(this, this.axis);
            }
        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';

        var addEvent = H.addEvent,
            animObject = H.animObject,
            arrayMax = H.arrayMax,
            arrayMin = H.arrayMin,
            AxisPlotLineOrBandExtension = H.AxisPlotLineOrBandExtension,
            color = H.color,
            correctFloat = H.correctFloat,
            defaultOptions = H.defaultOptions,
            defined = H.defined,
            deg2rad = H.deg2rad,
            destroyObjectProperties = H.destroyObjectProperties,
            each = H.each,
            error = H.error,
            extend = H.extend,
            fireEvent = H.fireEvent,
            format = H.format,
            getMagnitude = H.getMagnitude,
            grep = H.grep,
            inArray = H.inArray,
            isArray = H.isArray,
            isNumber = H.isNumber,
            isString = H.isString,
            merge = H.merge,
            normalizeTickInterval = H.normalizeTickInterval,
            pick = H.pick,
            PlotLineOrBand = H.PlotLineOrBand,
            removeEvent = H.removeEvent,
            splat = H.splat,
            syncTimeout = H.syncTimeout,
            Tick = H.Tick;

        /**
         * Create a new axis object.
         * @constructor Axis
         * @param {Object} chart
         * @param {Object} options
         */
        H.Axis = function() {
            this.init.apply(this, arguments);
        };

        H.Axis.prototype = {

            /**
             * Default options for the X axis - the Y axis has extended defaults
             */
            defaultOptions: {
                // allowDecimals: null,
                // alternateGridColor: null,
                // categories: [],
                dateTimeLabelFormats: {
                    millisecond: '%H:%M:%S.%L',
                    second: '%H:%M:%S',
                    minute: '%H:%M',
                    hour: '%H:%M',
                    day: '%e. %b',
                    week: '%e. %b',
                    month: '%b \'%y',
                    year: '%Y'
                },
                endOnTick: false,
                // reversed: false,

                labels: {
                    enabled: true,
                    // rotation: 0,
                    // align: 'center',
                    // step: null,

                    style: {
                        color: '#666666',
                        cursor: 'default',
                        fontSize: '11px'
                    },

                    x: 0
                        //y: undefined
                        /*formatter: function () {
                        	return this.value;
                        },*/
                },
                //linkedTo: null,
                //max: undefined,
                //min: undefined,
                minPadding: 0.01,
                maxPadding: 0.01,
                //minRange: null,
                //minorTickInterval: null,
                minorTickLength: 2,
                minorTickPosition: 'outside', // inside or outside
                //opposite: false,
                //offset: 0,
                //plotBands: [{
                //	events: {},
                //	zIndex: 1,
                //	labels: { align, x, verticalAlign, y, style, rotation, textAlign }
                //}],
                //plotLines: [{
                //	events: {}
                //  dashStyle: {}
                //	zIndex:
                //	labels: { align, x, verticalAlign, y, style, rotation, textAlign }
                //}],
                //reversed: false,
                // showFirstLabel: true,
                // showLastLabel: true,
                startOfWeek: 1,
                startOnTick: false,
                //tickInterval: null,
                tickLength: 10,
                tickmarkPlacement: 'between', // on or between
                tickPixelInterval: 100,
                tickPosition: 'outside',
                title: {
                    //text: null,
                    align: 'middle', // low, middle or high
                    //margin: 0 for horizontal, 10 for vertical axes,
                    //rotation: 0,
                    //side: 'outside',

                    style: {
                        color: '#666666'
                    }

                    //x: 0,
                    //y: 0
                },
                type: 'linear', // linear, logarithmic or datetime
                //visible: true

                minorGridLineColor: '#f2f2f2',
                // minorGridLineDashStyle: null,
                minorGridLineWidth: 1,
                minorTickColor: '#999999',
                //minorTickWidth: 0,
                lineColor: '#ccd6eb',
                lineWidth: 1,
                gridLineColor: '#e6e6e6',
                // gridLineDashStyle: 'solid',
                // gridLineWidth: 0,
                tickColor: '#ccd6eb'
                    // tickWidth: 1

            },

            /**
             * This options set extends the defaultOptions for Y axes
             */
            defaultYAxisOptions: {
                endOnTick: true,
                tickPixelInterval: 72,
                showLastLabel: true,
                labels: {
                    x: -8
                },
                maxPadding: 0.05,
                minPadding: 0.05,
                startOnTick: true,
                title: {
                    rotation: 270,
                    text: 'Values'
                },
                stackLabels: {
                    enabled: false,
                    //align: dynamic,
                    //y: dynamic,
                    //x: dynamic,
                    //verticalAlign: dynamic,
                    //textAlign: dynamic,
                    //rotation: 0,
                    formatter: function() {
                        return H.numberFormat(this.total, -1);
                    },

                    style: {
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: '#000000',
                        textOutline: '1px contrast'
                    }

                },

                gridLineWidth: 1,
                lineWidth: 0
                    // tickWidth: 0

            },

            /**
             * These options extend the defaultOptions for left axes
             */
            defaultLeftAxisOptions: {
                labels: {
                    x: -15
                },
                title: {
                    rotation: 270
                }
            },

            /**
             * These options extend the defaultOptions for right axes
             */
            defaultRightAxisOptions: {
                labels: {
                    x: 15
                },
                title: {
                    rotation: 90
                }
            },

            /**
             * These options extend the defaultOptions for bottom axes
             */
            defaultBottomAxisOptions: {
                labels: {
                    autoRotation: [-45],
                    x: 0
                        // overflow: undefined,
                        // staggerLines: null
                },
                title: {
                    rotation: 0
                }
            },
            /**
             * These options extend the defaultOptions for top axes
             */
            defaultTopAxisOptions: {
                labels: {
                    autoRotation: [-45],
                    x: 0
                        // overflow: undefined
                        // staggerLines: null
                },
                title: {
                    rotation: 0
                }
            },

            /**
             * Initialize the axis
             */
            init: function(chart, userOptions) {


                var isXAxis = userOptions.isX,
                    axis = this;

                axis.chart = chart;

                // Flag, is the axis horizontal
                axis.horiz = chart.inverted ? !isXAxis : isXAxis;

                // Flag, isXAxis
                axis.isXAxis = isXAxis;
                axis.coll = axis.coll || (isXAxis ? 'xAxis' : 'yAxis');

                axis.opposite = userOptions.opposite; // needed in setOptions
                axis.side = userOptions.side || (axis.horiz ?
                    (axis.opposite ? 0 : 2) : // top : bottom
                    (axis.opposite ? 1 : 3)); // right : left

                axis.setOptions(userOptions);


                var options = this.options,
                    type = options.type,
                    isDatetimeAxis = type === 'datetime';

                axis.labelFormatter = options.labels.formatter || axis.defaultLabelFormatter; // can be overwritten by dynamic format


                // Flag, stagger lines or not
                axis.userOptions = userOptions;

                //axis.axisTitleMargin = undefined,// = options.title.margin,
                axis.minPixelPadding = 0;

                axis.reversed = options.reversed;
                axis.visible = options.visible !== false;
                axis.zoomEnabled = options.zoomEnabled !== false;

                // Initial categories
                axis.hasNames = type === 'category' || options.categories === true;
                axis.categories = options.categories || axis.hasNames;
                axis.names = axis.names || []; // Preserve on update (#3830)

                // Elements
                //axis.axisGroup = undefined;
                //axis.gridGroup = undefined;
                //axis.axisTitle = undefined;
                //axis.axisLine = undefined;

                // Shorthand types
                axis.isLog = type === 'logarithmic';
                axis.isDatetimeAxis = isDatetimeAxis;

                // Flag, if axis is linked to another axis
                axis.isLinked = defined(options.linkedTo);
                // Linked axis.
                //axis.linkedParent = undefined;

                // Tick positions
                //axis.tickPositions = undefined; // array containing predefined positions
                // Tick intervals
                //axis.tickInterval = undefined;
                //axis.minorTickInterval = undefined;


                // Major ticks
                axis.ticks = {};
                axis.labelEdge = [];
                // Minor ticks
                axis.minorTicks = {};

                // List of plotLines/Bands
                axis.plotLinesAndBands = [];

                // Alternate bands
                axis.alternateBands = {};

                // Axis metrics
                //axis.left = undefined;
                //axis.top = undefined;
                //axis.width = undefined;
                //axis.height = undefined;
                //axis.bottom = undefined;
                //axis.right = undefined;
                //axis.transA = undefined;
                //axis.transB = undefined;
                //axis.oldTransA = undefined;
                axis.len = 0;
                //axis.oldMin = undefined;
                //axis.oldMax = undefined;
                //axis.oldUserMin = undefined;
                //axis.oldUserMax = undefined;
                //axis.oldAxisLength = undefined;
                axis.minRange = axis.userMinRange = options.minRange || options.maxZoom;
                axis.range = options.range;
                axis.offset = options.offset || 0;


                // Dictionary for stacks
                axis.stacks = {};
                axis.oldStacks = {};
                axis.stacksTouched = 0;

                // Min and max in the data
                //axis.dataMin = undefined,
                //axis.dataMax = undefined,

                // The axis range
                axis.max = null;
                axis.min = null;

                // User set min and max
                //axis.userMin = undefined,
                //axis.userMax = undefined,

                // Crosshair options
                axis.crosshair = pick(options.crosshair, splat(chart.options.tooltip.crosshairs)[isXAxis ? 0 : 1], false);
                // Run Axis

                var eventType,
                    events = axis.options.events;

                // Register
                if (inArray(axis, chart.axes) === -1) { // don't add it again on Axis.update()
                    if (isXAxis) { // #2713
                        chart.axes.splice(chart.xAxis.length, 0, axis);
                    } else {
                        chart.axes.push(axis);
                    }

                    chart[axis.coll].push(axis);
                }

                axis.series = axis.series || []; // populated by Series

                // inverted charts have reversed xAxes as default
                if (chart.inverted && isXAxis && axis.reversed === undefined) {
                    axis.reversed = true;
                }

                axis.removePlotBand = axis.removePlotBandOrLine;
                axis.removePlotLine = axis.removePlotBandOrLine;


                // register event listeners
                for (eventType in events) {
                    addEvent(axis, eventType, events[eventType]);
                }

                // extend logarithmic axis
                if (axis.isLog) {
                    axis.val2lin = axis.log2lin;
                    axis.lin2val = axis.lin2log;
                }
            },

            /**
             * Merge and set options
             */
            setOptions: function(userOptions) {
                this.options = merge(
                    this.defaultOptions,
                    this.coll === 'yAxis' && this.defaultYAxisOptions, [this.defaultTopAxisOptions, this.defaultRightAxisOptions,
                        this.defaultBottomAxisOptions, this.defaultLeftAxisOptions
                    ][this.side],
                    merge(
                        defaultOptions[this.coll], // if set in setOptions (#1053)
                        userOptions
                    )
                );
            },

            /**
             * The default label formatter. The context is a special config object for the label.
             */
            defaultLabelFormatter: function() {
                var axis = this.axis,
                    value = this.value,
                    categories = axis.categories,
                    dateTimeLabelFormat = this.dateTimeLabelFormat,
                    lang = defaultOptions.lang,
                    numericSymbols = lang.numericSymbols,
                    numSymMagnitude = lang.numericSymbolMagnitude || 1000,
                    i = numericSymbols && numericSymbols.length,
                    multi,
                    ret,
                    formatOption = axis.options.labels.format,

                    // make sure the same symbol is added for all labels on a linear axis
                    numericSymbolDetector = axis.isLog ? value : axis.tickInterval;

                if (formatOption) {
                    ret = format(formatOption, this);

                } else if (categories) {
                    ret = value;

                } else if (dateTimeLabelFormat) { // datetime axis
                    ret = H.dateFormat(dateTimeLabelFormat, value);

                } else if (i && numericSymbolDetector >= 1000) {
                    // Decide whether we should add a numeric symbol like k (thousands) or M (millions).
                    // If we are to enable this in tooltip or other places as well, we can move this
                    // logic to the numberFormatter and enable it by a parameter.
                    while (i-- && ret === undefined) {
                        multi = Math.pow(numSymMagnitude, i + 1);
                        if (numericSymbolDetector >= multi && (value * 10) % multi === 0 && numericSymbols[i] !== null && value !== 0) { // #5480
                            ret = H.numberFormat(value / multi, -1) + numericSymbols[i];
                        }
                    }
                }

                if (ret === undefined) {
                    if (Math.abs(value) >= 10000) { // add thousands separators
                        ret = H.numberFormat(value, -1);
                    } else { // small numbers
                        ret = H.numberFormat(value, -1, undefined, ''); // #2466
                    }
                }

                return ret;
            },

            /**
             * Get the minimum and maximum for the series of each axis
             */
            getSeriesExtremes: function() {
                var axis = this,
                    chart = axis.chart;
                axis.hasVisibleSeries = false;

                // Reset properties in case we're redrawing (#3353)
                axis.dataMin = axis.dataMax = axis.threshold = null;
                axis.softThreshold = !axis.isXAxis;

                if (axis.buildStacks) {
                    axis.buildStacks();
                }

                // loop through this axis' series
                each(axis.series, function(series) {

                    if (series.visible || !chart.options.chart.ignoreHiddenSeries) {

                        var seriesOptions = series.options,
                            xData,
                            threshold = seriesOptions.threshold,
                            seriesDataMin,
                            seriesDataMax;

                        axis.hasVisibleSeries = true;

                        // Validate threshold in logarithmic axes
                        if (axis.isLog && threshold <= 0) {
                            threshold = null;
                        }

                        // Get dataMin and dataMax for X axes
                        if (axis.isXAxis) {
                            xData = series.xData;
                            if (xData.length) {
                                // If xData contains values which is not numbers, then filter them out.
                                // To prevent performance hit, we only do this after we have already
                                // found seriesDataMin because in most cases all data is valid. #5234.
                                seriesDataMin = arrayMin(xData);
                                if (!isNumber(seriesDataMin) && !(seriesDataMin instanceof Date)) { // Date for #5010
                                    xData = grep(xData, function(x) {
                                        return isNumber(x);
                                    });
                                    seriesDataMin = arrayMin(xData); // Do it again with valid data
                                }

                                axis.dataMin = Math.min(pick(axis.dataMin, xData[0]), seriesDataMin);
                                axis.dataMax = Math.max(pick(axis.dataMax, xData[0]), arrayMax(xData));

                            }

                            // Get dataMin and dataMax for Y axes, as well as handle stacking and processed data
                        } else {

                            // Get this particular series extremes
                            series.getExtremes();
                            seriesDataMax = series.dataMax;
                            seriesDataMin = series.dataMin;

                            // Get the dataMin and dataMax so far. If percentage is used, the min and max are
                            // always 0 and 100. If seriesDataMin and seriesDataMax is null, then series
                            // doesn't have active y data, we continue with nulls
                            if (defined(seriesDataMin) && defined(seriesDataMax)) {
                                axis.dataMin = Math.min(pick(axis.dataMin, seriesDataMin), seriesDataMin);
                                axis.dataMax = Math.max(pick(axis.dataMax, seriesDataMax), seriesDataMax);
                            }

                            // Adjust to threshold
                            if (defined(threshold)) {
                                axis.threshold = threshold;
                            }
                            // If any series has a hard threshold, it takes precedence
                            if (!seriesOptions.softThreshold || axis.isLog) {
                                axis.softThreshold = false;
                            }
                        }
                    }
                });
            },

            /**
             * Translate from axis value to pixel position on the chart, or back
             *
             */
            translate: function(val, backwards, cvsCoord, old, handleLog, pointPlacement) {
                var axis = this.linkedParent || this, // #1417
                    sign = 1,
                    cvsOffset = 0,
                    localA = old ? axis.oldTransA : axis.transA,
                    localMin = old ? axis.oldMin : axis.min,
                    returnValue,
                    minPixelPadding = axis.minPixelPadding,
                    doPostTranslate = (axis.isOrdinal || axis.isBroken || (axis.isLog && handleLog)) && axis.lin2val;

                if (!localA) {
                    localA = axis.transA;
                }

                // In vertical axes, the canvas coordinates start from 0 at the top like in
                // SVG.
                if (cvsCoord) {
                    sign *= -1; // canvas coordinates inverts the value
                    cvsOffset = axis.len;
                }

                // Handle reversed axis
                if (axis.reversed) {
                    sign *= -1;
                    cvsOffset -= sign * (axis.sector || axis.len);
                }

                // From pixels to value
                if (backwards) { // reverse translation

                    val = val * sign + cvsOffset;
                    val -= minPixelPadding;
                    returnValue = val / localA + localMin; // from chart pixel to value
                    if (doPostTranslate) { // log and ordinal axes
                        returnValue = axis.lin2val(returnValue);
                    }

                    // From value to pixels
                } else {
                    if (doPostTranslate) { // log and ordinal axes
                        val = axis.val2lin(val);
                    }
                    returnValue = sign * (val - localMin) * localA + cvsOffset +
                        (sign * minPixelPadding) +
                        (isNumber(pointPlacement) ? localA * pointPlacement : 0);
                }

                return returnValue;
            },

            /**
             * Utility method to translate an axis value to pixel position.
             * @param {Number} value A value in terms of axis units
             * @param {Boolean} paneCoordinates Whether to return the pixel coordinate relative to the chart
             *        or just the axis/pane itself.
             */
            toPixels: function(value, paneCoordinates) {
                return this.translate(value, false, !this.horiz, null, true) + (paneCoordinates ? 0 : this.pos);
            },

            /*
             * Utility method to translate a pixel position in to an axis value
             * @param {Number} pixel The pixel value coordinate
             * @param {Boolean} paneCoordiantes Whether the input pixel is relative to the chart or just the
             *        axis/pane itself.
             */
            toValue: function(pixel, paneCoordinates) {
                return this.translate(pixel - (paneCoordinates ? 0 : this.pos), true, !this.horiz, null, true);
            },

            /**
             * Create the path for a plot line that goes from the given value on
             * this axis, across the plot to the opposite side
             * @param {Number} value
             * @param {Number} lineWidth Used for calculation crisp line
             * @param {Number] old Use old coordinates (for resizing and rescaling)
             */
            getPlotLinePath: function(value, lineWidth, old, force, translatedValue) {
                var axis = this,
                    chart = axis.chart,
                    axisLeft = axis.left,
                    axisTop = axis.top,
                    x1,
                    y1,
                    x2,
                    y2,
                    cHeight = (old && chart.oldChartHeight) || chart.chartHeight,
                    cWidth = (old && chart.oldChartWidth) || chart.chartWidth,
                    skip,
                    transB = axis.transB,
                    /**
                     * Check if x is between a and b. If not, either move to a/b or skip,
                     * depending on the force parameter.
                     */
                    between = function(x, a, b) {
                        if (x < a || x > b) {
                            if (force) {
                                x = Math.min(Math.max(a, x), b);
                            } else {
                                skip = true;
                            }
                        }
                        return x;
                    };

                translatedValue = pick(translatedValue, axis.translate(value, null, null, old));
                x1 = x2 = Math.round(translatedValue + transB);
                y1 = y2 = Math.round(cHeight - translatedValue - transB);
                if (!isNumber(translatedValue)) { // no min or max
                    skip = true;

                } else if (axis.horiz) {
                    y1 = axisTop;
                    y2 = cHeight - axis.bottom;
                    x1 = x2 = between(x1, axisLeft, axisLeft + axis.width);
                } else {
                    x1 = axisLeft;
                    x2 = cWidth - axis.right;
                    y1 = y2 = between(y1, axisTop, axisTop + axis.height);
                }
                return skip && !force ?
                    null :
                    chart.renderer.crispLine(['M', x1, y1, 'L', x2, y2], lineWidth || 1);
            },

            /**
             * Set the tick positions of a linear axis to round values like whole tens or every five.
             */
            getLinearTickPositions: function(tickInterval, min, max) {
                var pos,
                    lastPos,
                    roundedMin = correctFloat(Math.floor(min / tickInterval) * tickInterval),
                    roundedMax = correctFloat(Math.ceil(max / tickInterval) * tickInterval),
                    tickPositions = [];

                // For single points, add a tick regardless of the relative position (#2662)
                if (min === max && isNumber(min)) {
                    return [min];
                }

                // Populate the intermediate values
                pos = roundedMin;
                while (pos <= roundedMax) {

                    // Place the tick on the rounded value
                    tickPositions.push(pos);

                    // Always add the raw tickInterval, not the corrected one.
                    pos = correctFloat(pos + tickInterval);

                    // If the interval is not big enough in the current min - max range to actually increase
                    // the loop variable, we need to break out to prevent endless loop. Issue #619
                    if (pos === lastPos) {
                        break;
                    }

                    // Record the last value
                    lastPos = pos;
                }
                return tickPositions;
            },

            /**
             * Return the minor tick positions. For logarithmic axes, reuse the same logic
             * as for major ticks.
             */
            getMinorTickPositions: function() {
                var axis = this,
                    options = axis.options,
                    tickPositions = axis.tickPositions,
                    minorTickInterval = axis.minorTickInterval,
                    minorTickPositions = [],
                    pos,
                    i,
                    pointRangePadding = axis.pointRangePadding || 0,
                    min = axis.min - pointRangePadding, // #1498
                    max = axis.max + pointRangePadding, // #1498
                    range = max - min,
                    len;

                // If minor ticks get too dense, they are hard to read, and may cause long running script. So we don't draw them.
                if (range && range / minorTickInterval < axis.len / 3) { // #3875

                    if (axis.isLog) {
                        len = tickPositions.length;
                        for (i = 1; i < len; i++) {
                            minorTickPositions = minorTickPositions.concat(
                                axis.getLogTickPositions(minorTickInterval, tickPositions[i - 1], tickPositions[i], true)
                            );
                        }
                    } else if (axis.isDatetimeAxis && options.minorTickInterval === 'auto') { // #1314
                        minorTickPositions = minorTickPositions.concat(
                            axis.getTimeTicks(
                                axis.normalizeTimeTickInterval(minorTickInterval),
                                min,
                                max,
                                options.startOfWeek
                            )
                        );
                    } else {
                        for (
                            pos = min + (tickPositions[0] - min) % minorTickInterval; pos <= max; pos += minorTickInterval
                        ) {
                            // Very, very, tight grid lines (#5771)
                            if (pos === minorTickPositions[0]) {
                                break;
                            }
                            minorTickPositions.push(pos);
                        }
                    }
                }

                if (minorTickPositions.length !== 0) { // don't change the extremes, when there is no minor ticks
                    axis.trimTicks(minorTickPositions, options.startOnTick, options.endOnTick); // #3652 #3743 #1498
                }
                return minorTickPositions;
            },

            /**
             * Adjust the min and max for the minimum range. Keep in mind that the series data is
             * not yet processed, so we don't have information on data cropping and grouping, or
             * updated axis.pointRange or series.pointRange. The data can't be processed until
             * we have finally established min and max.
             */
            adjustForMinRange: function() {
                var axis = this,
                    options = axis.options,
                    min = axis.min,
                    max = axis.max,
                    zoomOffset,
                    spaceAvailable = axis.dataMax - axis.dataMin >= axis.minRange,
                    closestDataRange,
                    i,
                    distance,
                    xData,
                    loopLength,
                    minArgs,
                    maxArgs,
                    minRange;

                // Set the automatic minimum range based on the closest point distance
                if (axis.isXAxis && axis.minRange === undefined && !axis.isLog) {

                    if (defined(options.min) || defined(options.max)) {
                        axis.minRange = null; // don't do this again

                    } else {

                        // Find the closest distance between raw data points, as opposed to
                        // closestPointRange that applies to processed points (cropped and grouped)
                        each(axis.series, function(series) {
                            xData = series.xData;
                            loopLength = series.xIncrement ? 1 : xData.length - 1;
                            for (i = loopLength; i > 0; i--) {
                                distance = xData[i] - xData[i - 1];
                                if (closestDataRange === undefined || distance < closestDataRange) {
                                    closestDataRange = distance;
                                }
                            }
                        });
                        axis.minRange = Math.min(closestDataRange * 5, axis.dataMax - axis.dataMin);
                    }
                }

                // if minRange is exceeded, adjust
                if (max - min < axis.minRange) {
                    minRange = axis.minRange;
                    zoomOffset = (minRange - max + min) / 2;

                    // if min and max options have been set, don't go beyond it
                    minArgs = [min - zoomOffset, pick(options.min, min - zoomOffset)];
                    if (spaceAvailable) { // if space is available, stay within the data range
                        minArgs[2] = axis.isLog ? axis.log2lin(axis.dataMin) : axis.dataMin;
                    }
                    min = arrayMax(minArgs);

                    maxArgs = [min + minRange, pick(options.max, min + minRange)];
                    if (spaceAvailable) { // if space is availabe, stay within the data range
                        maxArgs[2] = axis.isLog ? axis.log2lin(axis.dataMax) : axis.dataMax;
                    }

                    max = arrayMin(maxArgs);

                    // now if the max is adjusted, adjust the min back
                    if (max - min < minRange) {
                        minArgs[0] = max - minRange;
                        minArgs[1] = pick(options.min, max - minRange);
                        min = arrayMax(minArgs);
                    }
                }

                // Record modified extremes
                axis.min = min;
                axis.max = max;
            },

            /**
             * Find the closestPointRange across all series
             */
            getClosest: function() {
                var ret;

                if (this.categories) {
                    ret = 1;
                } else {
                    each(this.series, function(series) {
                        var seriesClosest = series.closestPointRange,
                            visible = series.visible ||
                            !series.chart.options.chart.ignoreHiddenSeries;

                        if (!series.noSharedTooltip &&
                            defined(seriesClosest) &&
                            visible
                        ) {
                            ret = defined(ret) ?
                                Math.min(ret, seriesClosest) :
                                seriesClosest;
                        }
                    });
                }
                return ret;
            },

            /**
             * When a point name is given and no x, search for the name in the existing categories,
             * or if categories aren't provided, search names or create a new category (#2522).
             */
            nameToX: function(point) {
                var explicitCategories = isArray(this.categories),
                    names = explicitCategories ? this.categories : this.names,
                    nameX = point.options.x,
                    x;

                point.series.requireSorting = false;

                if (!defined(nameX)) {
                    nameX = this.options.uniqueNames === false ?
                        point.series.autoIncrement() :
                        inArray(point.name, names);
                }
                if (nameX === -1) { // The name is not found in currenct categories
                    if (!explicitCategories) {
                        x = names.length;
                    }
                } else {
                    x = nameX;
                }

                // Write the last point's name to the names array
                this.names[x] = point.name;

                return x;
            },

            /**
             * When changes have been done to series data, update the axis.names.
             */
            updateNames: function() {
                var axis = this;

                if (this.names.length > 0) {
                    this.names.length = 0;
                    this.minRange = undefined;
                    each(this.series || [], function(series) {

                        // Reset incrementer (#5928)
                        series.xIncrement = null;

                        // When adding a series, points are not yet generated
                        if (!series.points || series.isDirtyData) {
                            series.processData();
                            series.generatePoints();
                        }

                        each(series.points, function(point, i) {
                            var x;
                            if (point.options && point.options.x === undefined) {
                                x = axis.nameToX(point);
                                if (x !== point.x) {
                                    point.x = x;
                                    series.xData[i] = x;
                                }
                            }
                        });
                    });
                }
            },

            /**
             * Update translation information
             */
            setAxisTranslation: function(saveOld) {
                var axis = this,
                    range = axis.max - axis.min,
                    pointRange = axis.axisPointRange || 0,
                    closestPointRange,
                    minPointOffset = 0,
                    pointRangePadding = 0,
                    linkedParent = axis.linkedParent,
                    ordinalCorrection,
                    hasCategories = !!axis.categories,
                    transA = axis.transA,
                    isXAxis = axis.isXAxis;

                // Adjust translation for padding. Y axis with categories need to go through the same (#1784).
                if (isXAxis || hasCategories || pointRange) {
                    if (linkedParent) {
                        minPointOffset = linkedParent.minPointOffset;
                        pointRangePadding = linkedParent.pointRangePadding;

                    } else {

                        // Get the closest points
                        closestPointRange = axis.getClosest();

                        each(axis.series, function(series) {
                            var seriesPointRange = hasCategories ?
                                1 :
                                (isXAxis ?
                                    pick(series.options.pointRange, closestPointRange, 0) :
                                    (axis.axisPointRange || 0)), // #2806
                                pointPlacement = series.options.pointPlacement;

                            pointRange = Math.max(pointRange, seriesPointRange);

                            if (!axis.single) {
                                // minPointOffset is the value padding to the left of the axis in order to make
                                // room for points with a pointRange, typically columns. When the pointPlacement option
                                // is 'between' or 'on', this padding does not apply.
                                minPointOffset = Math.max(
                                    minPointOffset,
                                    isString(pointPlacement) ? 0 : seriesPointRange / 2
                                );

                                // Determine the total padding needed to the length of the axis to make room for the
                                // pointRange. If the series' pointPlacement is 'on', no padding is added.
                                pointRangePadding = Math.max(
                                    pointRangePadding,
                                    pointPlacement === 'on' ? 0 : seriesPointRange
                                );
                            }
                        });
                    }

                    // Record minPointOffset and pointRangePadding
                    ordinalCorrection = axis.ordinalSlope && closestPointRange ? axis.ordinalSlope / closestPointRange : 1; // #988, #1853
                    axis.minPointOffset = minPointOffset = minPointOffset * ordinalCorrection;
                    axis.pointRangePadding = pointRangePadding = pointRangePadding * ordinalCorrection;

                    // pointRange means the width reserved for each point, like in a column chart
                    axis.pointRange = Math.min(pointRange, range);

                    // closestPointRange means the closest distance between points. In columns
                    // it is mostly equal to pointRange, but in lines pointRange is 0 while closestPointRange
                    // is some other value
                    if (isXAxis) {
                        axis.closestPointRange = closestPointRange;
                    }
                }

                // Secondary values
                if (saveOld) {
                    axis.oldTransA = transA;
                }
                axis.translationSlope = axis.transA = transA = axis.len / ((range + pointRangePadding) || 1);
                axis.transB = axis.horiz ? axis.left : axis.bottom; // translation addend
                axis.minPixelPadding = transA * minPointOffset;
            },

            minFromRange: function() {
                return this.max - this.range;
            },

            /**
             * Set the tick positions to round values and optionally extend the extremes
             * to the nearest tick
             */
            setTickInterval: function(secondPass) {
                var axis = this,
                    chart = axis.chart,
                    options = axis.options,
                    isLog = axis.isLog,
                    log2lin = axis.log2lin,
                    isDatetimeAxis = axis.isDatetimeAxis,
                    isXAxis = axis.isXAxis,
                    isLinked = axis.isLinked,
                    maxPadding = options.maxPadding,
                    minPadding = options.minPadding,
                    length,
                    linkedParentExtremes,
                    tickIntervalOption = options.tickInterval,
                    minTickInterval,
                    tickPixelIntervalOption = options.tickPixelInterval,
                    categories = axis.categories,
                    threshold = axis.threshold,
                    softThreshold = axis.softThreshold,
                    thresholdMin,
                    thresholdMax,
                    hardMin,
                    hardMax;

                if (!isDatetimeAxis && !categories && !isLinked) {
                    this.getTickAmount();
                }

                // Min or max set either by zooming/setExtremes or initial options
                hardMin = pick(axis.userMin, options.min);
                hardMax = pick(axis.userMax, options.max);

                // Linked axis gets the extremes from the parent axis
                if (isLinked) {
                    axis.linkedParent = chart[axis.coll][options.linkedTo];
                    linkedParentExtremes = axis.linkedParent.getExtremes();
                    axis.min = pick(linkedParentExtremes.min, linkedParentExtremes.dataMin);
                    axis.max = pick(linkedParentExtremes.max, linkedParentExtremes.dataMax);
                    if (options.type !== axis.linkedParent.options.type) {
                        error(11, 1); // Can't link axes of different type
                    }

                    // Initial min and max from the extreme data values
                } else {

                    // Adjust to hard threshold
                    if (!softThreshold && defined(threshold)) {
                        if (axis.dataMin >= threshold) {
                            thresholdMin = threshold;
                            minPadding = 0;
                        } else if (axis.dataMax <= threshold) {
                            thresholdMax = threshold;
                            maxPadding = 0;
                        }
                    }

                    axis.min = pick(hardMin, thresholdMin, axis.dataMin);
                    axis.max = pick(hardMax, thresholdMax, axis.dataMax);

                }

                if (isLog) {
                    if (!secondPass && Math.min(axis.min, pick(axis.dataMin, axis.min)) <= 0) { // #978
                        error(10, 1); // Can't plot negative values on log axis
                    }
                    // The correctFloat cures #934, float errors on full tens. But it
                    // was too aggressive for #4360 because of conversion back to lin,
                    // therefore use precision 15.
                    axis.min = correctFloat(log2lin(axis.min), 15);
                    axis.max = correctFloat(log2lin(axis.max), 15);
                }

                // handle zoomed range
                if (axis.range && defined(axis.max)) {
                    axis.userMin = axis.min = hardMin = Math.max(axis.min, axis.minFromRange()); // #618
                    axis.userMax = hardMax = axis.max;

                    axis.range = null; // don't use it when running setExtremes
                }

                // Hook for Highstock Scroller. Consider combining with beforePadding.
                fireEvent(axis, 'foundExtremes');

                // Hook for adjusting this.min and this.max. Used by bubble series.
                if (axis.beforePadding) {
                    axis.beforePadding();
                }

                // adjust min and max for the minimum range
                axis.adjustForMinRange();

                // Pad the values to get clear of the chart's edges. To avoid tickInterval taking the padding
                // into account, we do this after computing tick interval (#1337).
                if (!categories && !axis.axisPointRange && !axis.usePercentage && !isLinked && defined(axis.min) && defined(axis.max)) {
                    length = axis.max - axis.min;
                    if (length) {
                        if (!defined(hardMin) && minPadding) {
                            axis.min -= length * minPadding;
                        }
                        if (!defined(hardMax) && maxPadding) {
                            axis.max += length * maxPadding;
                        }
                    }
                }

                // Handle options for floor, ceiling, softMin and softMax
                if (isNumber(options.floor)) {
                    axis.min = Math.max(axis.min, options.floor);
                } else if (isNumber(options.softMin)) {
                    axis.min = Math.min(axis.min, options.softMin);
                }
                if (isNumber(options.ceiling)) {
                    axis.max = Math.min(axis.max, options.ceiling);
                } else if (isNumber(options.softMax)) {
                    axis.max = Math.max(axis.max, options.softMax);
                }

                // When the threshold is soft, adjust the extreme value only if
                // the data extreme and the padded extreme land on either side of the threshold. For example,
                // a series of [0, 1, 2, 3] would make the yAxis add a tick for -1 because of the
                // default minPadding and startOnTick options. This is prevented by the softThreshold
                // option.
                if (softThreshold && defined(axis.dataMin)) {
                    threshold = threshold || 0;
                    if (!defined(hardMin) && axis.min < threshold && axis.dataMin >= threshold) {
                        axis.min = threshold;
                    } else if (!defined(hardMax) && axis.max > threshold && axis.dataMax <= threshold) {
                        axis.max = threshold;
                    }
                }


                // get tickInterval
                if (axis.min === axis.max || axis.min === undefined || axis.max === undefined) {
                    axis.tickInterval = 1;
                } else if (isLinked && !tickIntervalOption &&
                    tickPixelIntervalOption === axis.linkedParent.options.tickPixelInterval) {
                    axis.tickInterval = tickIntervalOption = axis.linkedParent.tickInterval;
                } else {
                    axis.tickInterval = pick(
                        tickIntervalOption,
                        this.tickAmount ? ((axis.max - axis.min) / Math.max(this.tickAmount - 1, 1)) : undefined,
                        categories ? // for categoried axis, 1 is default, for linear axis use tickPix
                        1 :
                        // don't let it be more than the data range
                        (axis.max - axis.min) * tickPixelIntervalOption / Math.max(axis.len, tickPixelIntervalOption)
                    );
                }

                // Now we're finished detecting min and max, crop and group series data. This
                // is in turn needed in order to find tick positions in ordinal axes.
                if (isXAxis && !secondPass) {
                    each(axis.series, function(series) {
                        series.processData(axis.min !== axis.oldMin || axis.max !== axis.oldMax);
                    });
                }

                // set the translation factor used in translate function
                axis.setAxisTranslation(true);

                // hook for ordinal axes and radial axes
                if (axis.beforeSetTickPositions) {
                    axis.beforeSetTickPositions();
                }

                // hook for extensions, used in Highstock ordinal axes
                if (axis.postProcessTickInterval) {
                    axis.tickInterval = axis.postProcessTickInterval(axis.tickInterval);
                }

                // In column-like charts, don't cramp in more ticks than there are points (#1943, #4184)
                if (axis.pointRange && !tickIntervalOption) {
                    axis.tickInterval = Math.max(axis.pointRange, axis.tickInterval);
                }

                // Before normalizing the tick interval, handle minimum tick interval. This applies only if tickInterval is not defined.
                minTickInterval = pick(options.minTickInterval, axis.isDatetimeAxis && axis.closestPointRange);
                if (!tickIntervalOption && axis.tickInterval < minTickInterval) {
                    axis.tickInterval = minTickInterval;
                }

                // for linear axes, get magnitude and normalize the interval
                if (!isDatetimeAxis && !isLog && !tickIntervalOption) {
                    axis.tickInterval = normalizeTickInterval(
                        axis.tickInterval,
                        null,
                        getMagnitude(axis.tickInterval),
                        // If the tick interval is between 0.5 and 5 and the axis max is in the order of
                        // thousands, chances are we are dealing with years. Don't allow decimals. #3363.
                        pick(options.allowDecimals, !(axis.tickInterval > 0.5 && axis.tickInterval < 5 && axis.max > 1000 && axis.max < 9999)), !!this.tickAmount
                    );
                }

                // Prevent ticks from getting so close that we can't draw the labels
                if (!this.tickAmount) {
                    axis.tickInterval = axis.unsquish();
                }

                this.setTickPositions();
            },

            /**
             * Now we have computed the normalized tickInterval, get the tick positions
             */
            setTickPositions: function() {

                var options = this.options,
                    tickPositions,
                    tickPositionsOption = options.tickPositions,
                    tickPositioner = options.tickPositioner,
                    startOnTick = options.startOnTick,
                    endOnTick = options.endOnTick,
                    single;

                // Set the tickmarkOffset
                this.tickmarkOffset = (this.categories && options.tickmarkPlacement === 'between' &&
                    this.tickInterval === 1) ? 0.5 : 0; // #3202


                // get minorTickInterval
                this.minorTickInterval = options.minorTickInterval === 'auto' && this.tickInterval ?
                    this.tickInterval / 5 : options.minorTickInterval;

                // Find the tick positions
                this.tickPositions = tickPositions = tickPositionsOption && tickPositionsOption.slice(); // Work on a copy (#1565)
                if (!tickPositions) {

                    if (this.isDatetimeAxis) {
                        tickPositions = this.getTimeTicks(
                            this.normalizeTimeTickInterval(this.tickInterval, options.units),
                            this.min,
                            this.max,
                            options.startOfWeek,
                            this.ordinalPositions,
                            this.closestPointRange,
                            true
                        );
                    } else if (this.isLog) {
                        tickPositions = this.getLogTickPositions(this.tickInterval, this.min, this.max);
                    } else {
                        tickPositions = this.getLinearTickPositions(this.tickInterval, this.min, this.max);
                    }

                    // Too dense ticks, keep only the first and last (#4477)
                    if (tickPositions.length > this.len) {
                        tickPositions = [tickPositions[0], tickPositions.pop()];
                    }

                    this.tickPositions = tickPositions;

                    // Run the tick positioner callback, that allows modifying auto tick positions.
                    if (tickPositioner) {
                        tickPositioner = tickPositioner.apply(this, [this.min, this.max]);
                        if (tickPositioner) {
                            this.tickPositions = tickPositions = tickPositioner;
                        }
                    }

                }

                if (!this.isLinked) {

                    // reset min/max or remove extremes based on start/end on tick
                    this.trimTicks(tickPositions, startOnTick, endOnTick);

                    // When there is only one point, or all points have the same value on this axis, then min
                    // and max are equal and tickPositions.length is 0 or 1. In this case, add some padding
                    // in order to center the point, but leave it with one tick. #1337.
                    if (this.min === this.max && defined(this.min) && !this.tickAmount) {
                        // Substract half a unit (#2619, #2846, #2515, #3390)
                        single = true;
                        this.min -= 0.5;
                        this.max += 0.5;
                    }
                    this.single = single;

                    if (!tickPositionsOption && !tickPositioner) {
                        this.adjustTickAmount();
                    }
                }
            },

            /**
             * Handle startOnTick and endOnTick by either adapting to padding min/max or rounded min/max
             */
            trimTicks: function(tickPositions, startOnTick, endOnTick) {
                var roundedMin = tickPositions[0],
                    roundedMax = tickPositions[tickPositions.length - 1],
                    minPointOffset = this.minPointOffset || 0;

                if (startOnTick) {
                    this.min = roundedMin;
                } else {
                    while (this.min - minPointOffset > tickPositions[0]) {
                        tickPositions.shift();
                    }
                }

                if (endOnTick) {
                    this.max = roundedMax;
                } else {
                    while (this.max + minPointOffset < tickPositions[tickPositions.length - 1]) {
                        tickPositions.pop();
                    }
                }

                // If no tick are left, set one tick in the middle (#3195)
                if (tickPositions.length === 0 && defined(roundedMin)) {
                    tickPositions.push((roundedMax + roundedMin) / 2);
                }
            },

            /**
             * Check if there are multiple axes in the same pane
             * @returns {Boolean} There are other axes
             */
            alignToOthers: function() {
                var others = {}, // Whether there is another axis to pair with this one
                    hasOther,
                    options = this.options;

                if (this.chart.options.chart.alignTicks !== false && options.alignTicks !== false) {
                    each(this.chart[this.coll], function(axis) {
                        var otherOptions = axis.options,
                            horiz = axis.horiz,
                            key = [
                                horiz ? otherOptions.left : otherOptions.top,
                                otherOptions.width,
                                otherOptions.height,
                                otherOptions.pane
                            ].join(',');


                        if (axis.series.length) { // #4442
                            if (others[key]) {
                                hasOther = true; // #4201
                            } else {
                                others[key] = 1;
                            }
                        }
                    });
                }
                return hasOther;
            },

            /**
             * Set the max ticks of either the x and y axis collection
             */
            getTickAmount: function() {
                var options = this.options,
                    tickAmount = options.tickAmount,
                    tickPixelInterval = options.tickPixelInterval;

                if (!defined(options.tickInterval) && this.len < tickPixelInterval && !this.isRadial &&
                    !this.isLog && options.startOnTick && options.endOnTick) {
                    tickAmount = 2;
                }

                if (!tickAmount && this.alignToOthers()) {
                    // Add 1 because 4 tick intervals require 5 ticks (including first and last)
                    tickAmount = Math.ceil(this.len / tickPixelInterval) + 1;
                }

                // For tick amounts of 2 and 3, compute five ticks and remove the intermediate ones. This
                // prevents the axis from adding ticks that are too far away from the data extremes.
                if (tickAmount < 4) {
                    this.finalTickAmt = tickAmount;
                    tickAmount = 5;
                }

                this.tickAmount = tickAmount;
            },

            /**
             * When using multiple axes, adjust the number of ticks to match the highest
             * number of ticks in that group
             */
            adjustTickAmount: function() {
                var tickInterval = this.tickInterval,
                    tickPositions = this.tickPositions,
                    tickAmount = this.tickAmount,
                    finalTickAmt = this.finalTickAmt,
                    currentTickAmount = tickPositions && tickPositions.length,
                    i,
                    len;

                if (currentTickAmount < tickAmount) {
                    while (tickPositions.length < tickAmount) {
                        tickPositions.push(correctFloat(
                            tickPositions[tickPositions.length - 1] + tickInterval
                        ));
                    }
                    this.transA *= (currentTickAmount - 1) / (tickAmount - 1);
                    this.max = tickPositions[tickPositions.length - 1];

                    // We have too many ticks, run second pass to try to reduce ticks
                } else if (currentTickAmount > tickAmount) {
                    this.tickInterval *= 2;
                    this.setTickPositions();
                }

                // The finalTickAmt property is set in getTickAmount
                if (defined(finalTickAmt)) {
                    i = len = tickPositions.length;
                    while (i--) {
                        if (
                            (finalTickAmt === 3 && i % 2 === 1) || // Remove every other tick
                            (finalTickAmt <= 2 && i > 0 && i < len - 1) // Remove all but first and last
                        ) {
                            tickPositions.splice(i, 1);
                        }
                    }
                    this.finalTickAmt = undefined;
                }
            },

            /**
             * Set the scale based on data min and max, user set min and max or options
             *
             */
            setScale: function() {
                var axis = this,
                    isDirtyData,
                    isDirtyAxisLength;

                axis.oldMin = axis.min;
                axis.oldMax = axis.max;
                axis.oldAxisLength = axis.len;

                // set the new axisLength
                axis.setAxisSize();
                //axisLength = horiz ? axisWidth : axisHeight;
                isDirtyAxisLength = axis.len !== axis.oldAxisLength;

                // is there new data?
                each(axis.series, function(series) {
                    if (series.isDirtyData || series.isDirty ||
                        series.xAxis.isDirty) { // when x axis is dirty, we need new data extremes for y as well
                        isDirtyData = true;
                    }
                });

                // do we really need to go through all this?
                if (isDirtyAxisLength || isDirtyData || axis.isLinked || axis.forceRedraw ||
                    axis.userMin !== axis.oldUserMin || axis.userMax !== axis.oldUserMax || axis.alignToOthers()) {

                    if (axis.resetStacks) {
                        axis.resetStacks();
                    }

                    axis.forceRedraw = false;

                    // get data extremes if needed
                    axis.getSeriesExtremes();

                    // get fixed positions based on tickInterval
                    axis.setTickInterval();

                    // record old values to decide whether a rescale is necessary later on (#540)
                    axis.oldUserMin = axis.userMin;
                    axis.oldUserMax = axis.userMax;

                    // Mark as dirty if it is not already set to dirty and extremes have changed. #595.
                    if (!axis.isDirty) {
                        axis.isDirty = isDirtyAxisLength || axis.min !== axis.oldMin || axis.max !== axis.oldMax;
                    }
                } else if (axis.cleanStacks) {
                    axis.cleanStacks();
                }
            },

            /**
             * Set the extremes and optionally redraw
             * @param {Number} newMin
             * @param {Number} newMax
             * @param {Boolean} redraw
             * @param {Boolean|Object} animation Whether to apply animation, and optionally animation
             *    configuration
             * @param {Object} eventArguments
             *
             */
            setExtremes: function(newMin, newMax, redraw, animation, eventArguments) {
                var axis = this,
                    chart = axis.chart;

                redraw = pick(redraw, true); // defaults to true

                each(axis.series, function(serie) {
                    delete serie.kdTree;
                });

                // Extend the arguments with min and max
                eventArguments = extend(eventArguments, {
                    min: newMin,
                    max: newMax
                });

                // Fire the event
                fireEvent(axis, 'setExtremes', eventArguments, function() { // the default event handler

                    axis.userMin = newMin;
                    axis.userMax = newMax;
                    axis.eventArgs = eventArguments;

                    if (redraw) {
                        chart.redraw(animation);
                    }
                });
            },

            /**
             * Overridable method for zooming chart. Pulled out in a separate method to allow overriding
             * in stock charts.
             */
            zoom: function(newMin, newMax) {
                var dataMin = this.dataMin,
                    dataMax = this.dataMax,
                    options = this.options,
                    min = Math.min(dataMin, pick(options.min, dataMin)),
                    max = Math.max(dataMax, pick(options.max, dataMax));

                if (newMin !== this.min || newMax !== this.max) { // #5790

                    // Prevent pinch zooming out of range. Check for defined is for #1946. #1734.
                    if (!this.allowZoomOutside) {
                        if (defined(dataMin) && newMin <= min) {
                            newMin = min;
                        }
                        if (defined(dataMax) && newMax >= max) {
                            newMax = max;
                        }
                    }

                    // In full view, displaying the reset zoom button is not required
                    this.displayBtn = newMin !== undefined || newMax !== undefined;

                    // Do it
                    this.setExtremes(
                        newMin,
                        newMax,
                        false,
                        undefined, {
                            trigger: 'zoom'
                        }
                    );
                }

                return true;
            },

            /**
             * Update the axis metrics
             */
            setAxisSize: function() {
                var chart = this.chart,
                    options = this.options,
                    offsetLeft = options.offsetLeft || 0,
                    offsetRight = options.offsetRight || 0,
                    horiz = this.horiz,
                    width = pick(options.width, chart.plotWidth - offsetLeft + offsetRight),
                    height = pick(options.height, chart.plotHeight),
                    top = pick(options.top, chart.plotTop),
                    left = pick(options.left, chart.plotLeft + offsetLeft),
                    percentRegex = /%$/;

                // Check for percentage based input values. Rounding fixes problems with
                // column overflow and plot line filtering (#4898, #4899)
                if (percentRegex.test(height)) {
                    height = Math.round(parseFloat(height) / 100 * chart.plotHeight);
                }
                if (percentRegex.test(top)) {
                    top = Math.round(parseFloat(top) / 100 * chart.plotHeight + chart.plotTop);
                }

                // Expose basic values to use in Series object and navigator
                this.left = left;
                this.top = top;
                this.width = width;
                this.height = height;
                this.bottom = chart.chartHeight - height - top;
                this.right = chart.chartWidth - width - left;

                // Direction agnostic properties
                this.len = Math.max(horiz ? width : height, 0); // Math.max fixes #905
                this.pos = horiz ? left : top; // distance from SVG origin
            },

            /**
             * Get the actual axis extremes
             */
            getExtremes: function() {
                var axis = this,
                    isLog = axis.isLog,
                    lin2log = axis.lin2log;

                return {
                    min: isLog ? correctFloat(lin2log(axis.min)) : axis.min,
                    max: isLog ? correctFloat(lin2log(axis.max)) : axis.max,
                    dataMin: axis.dataMin,
                    dataMax: axis.dataMax,
                    userMin: axis.userMin,
                    userMax: axis.userMax
                };
            },

            /**
             * Get the zero plane either based on zero or on the min or max value.
             * Used in bar and area plots
             */
            getThreshold: function(threshold) {
                var axis = this,
                    isLog = axis.isLog,
                    lin2log = axis.lin2log,
                    realMin = isLog ? lin2log(axis.min) : axis.min,
                    realMax = isLog ? lin2log(axis.max) : axis.max;

                if (threshold === null) {
                    threshold = realMin;
                } else if (realMin > threshold) {
                    threshold = realMin;
                } else if (realMax < threshold) {
                    threshold = realMax;
                }

                return axis.translate(threshold, 0, 1, 0, 1);
            },

            /**
             * Compute auto alignment for the axis label based on which side the axis is on
             * and the given rotation for the label
             */
            autoLabelAlign: function(rotation) {
                var ret,
                    angle = (pick(rotation, 0) - (this.side * 90) + 720) % 360;

                if (angle > 15 && angle < 165) {
                    ret = 'right';
                } else if (angle > 195 && angle < 345) {
                    ret = 'left';
                } else {
                    ret = 'center';
                }
                return ret;
            },

            /**
             * Get the tick length and width for the axis.
             * @param   {String} prefix 'tick' or 'minorTick'
             * @returns {Array}        An array of tickLength and tickWidth
             */
            tickSize: function(prefix) {
                var options = this.options,
                    tickLength = options[prefix + 'Length'],
                    tickWidth = pick(options[prefix + 'Width'], prefix === 'tick' && this.isXAxis ? 1 : 0); // X axis defaults to 1

                if (tickWidth && tickLength) {
                    // Negate the length
                    if (options[prefix + 'Position'] === 'inside') {
                        tickLength = -tickLength;
                    }
                    return [tickLength, tickWidth];
                }

            },

            /**
             * Return the size of the labels
             */
            labelMetrics: function() {
                return this.chart.renderer.fontMetrics(
                    this.options.labels.style && this.options.labels.style.fontSize,
                    this.ticks[0] && this.ticks[0].label
                );
            },

            /**
             * Prevent the ticks from getting so close we can't draw the labels. On a horizontal
             * axis, this is handled by rotating the labels, removing ticks and adding ellipsis.
             * On a vertical axis remove ticks and add ellipsis.
             */
            unsquish: function() {
                var labelOptions = this.options.labels,
                    horiz = this.horiz,
                    tickInterval = this.tickInterval,
                    newTickInterval = tickInterval,
                    slotSize = this.len / (((this.categories ? 1 : 0) + this.max - this.min) / tickInterval),
                    rotation,
                    rotationOption = labelOptions.rotation,
                    labelMetrics = this.labelMetrics(),
                    step,
                    bestScore = Number.MAX_VALUE,
                    autoRotation,
                    // Return the multiple of tickInterval that is needed to avoid collision
                    getStep = function(spaceNeeded) {
                        var step = spaceNeeded / (slotSize || 1);
                        step = step > 1 ? Math.ceil(step) : 1;
                        return step * tickInterval;
                    };

                if (horiz) {
                    autoRotation = !labelOptions.staggerLines && !labelOptions.step && ( // #3971
                        defined(rotationOption) ? [rotationOption] :
                        slotSize < pick(labelOptions.autoRotationLimit, 80) && labelOptions.autoRotation
                    );

                    if (autoRotation) {

                        // Loop over the given autoRotation options, and determine which gives the best score. The
                        // best score is that with the lowest number of steps and a rotation closest to horizontal.
                        each(autoRotation, function(rot) {
                            var score;

                            if (rot === rotationOption || (rot && rot >= -90 && rot <= 90)) { // #3891

                                step = getStep(Math.abs(labelMetrics.h / Math.sin(deg2rad * rot)));

                                score = step + Math.abs(rot / 360);

                                if (score < bestScore) {
                                    bestScore = score;
                                    rotation = rot;
                                    newTickInterval = step;
                                }
                            }
                        });
                    }

                } else if (!labelOptions.step) { // #4411
                    newTickInterval = getStep(labelMetrics.h);
                }

                this.autoRotation = autoRotation;
                this.labelRotation = pick(rotation, rotationOption);

                return newTickInterval;
            },

            /**
             * Get the general slot width for this axis. This may change between the pre-render (from Axis.getOffset) 
             * and the final tick rendering and placement (#5086).
             */
            getSlotWidth: function() {
                var chart = this.chart,
                    horiz = this.horiz,
                    labelOptions = this.options.labels,
                    slotCount = Math.max(this.tickPositions.length - (this.categories ? 0 : 1), 1),
                    marginLeft = chart.margin[3];

                return (horiz && (labelOptions.step || 0) < 2 && !labelOptions.rotation && // #4415
                        ((this.staggerLines || 1) * chart.plotWidth) / slotCount) ||
                    (!horiz && ((marginLeft && (marginLeft - chart.spacing[3])) || chart.chartWidth * 0.33)); // #1580, #1931

            },

            /**
             * Render the axis labels and determine whether ellipsis or rotation need to be applied
             */
            renderUnsquish: function() {
                var chart = this.chart,
                    renderer = chart.renderer,
                    tickPositions = this.tickPositions,
                    ticks = this.ticks,
                    labelOptions = this.options.labels,
                    horiz = this.horiz,
                    slotWidth = this.getSlotWidth(),
                    innerWidth = Math.max(1, Math.round(slotWidth - 2 * (labelOptions.padding || 5))),
                    attr = {},
                    labelMetrics = this.labelMetrics(),
                    textOverflowOption = labelOptions.style && labelOptions.style.textOverflow,
                    css,
                    maxLabelLength = 0,
                    label,
                    i,
                    pos;

                // Set rotation option unless it is "auto", like in gauges
                if (!isString(labelOptions.rotation)) {
                    attr.rotation = labelOptions.rotation || 0; // #4443
                }

                // Get the longest label length
                each(tickPositions, function(tick) {
                    tick = ticks[tick];
                    if (tick && tick.labelLength > maxLabelLength) {
                        maxLabelLength = tick.labelLength;
                    }
                });
                this.maxLabelLength = maxLabelLength;


                // Handle auto rotation on horizontal axis
                if (this.autoRotation) {

                    // Apply rotation only if the label is too wide for the slot, and
                    // the label is wider than its height.
                    if (maxLabelLength > innerWidth && maxLabelLength > labelMetrics.h) {
                        attr.rotation = this.labelRotation;
                    } else {
                        this.labelRotation = 0;
                    }

                    // Handle word-wrap or ellipsis on vertical axis
                } else if (slotWidth) {
                    // For word-wrap or ellipsis
                    css = {
                        width: innerWidth + 'px'
                    };

                    if (!textOverflowOption) {
                        css.textOverflow = 'clip';

                        // On vertical axis, only allow word wrap if there is room for more lines.
                        i = tickPositions.length;
                        while (!horiz && i--) {
                            pos = tickPositions[i];
                            label = ticks[pos].label;
                            if (label) {
                                // Reset ellipsis in order to get the correct bounding box (#4070)
                                if (label.styles && label.styles.textOverflow === 'ellipsis') {
                                    label.css({
                                        textOverflow: 'clip'
                                    });

                                    // Set the correct width in order to read the bounding box height (#4678, #5034)
                                } else if (ticks[pos].labelLength > slotWidth) {
                                    label.css({
                                        width: slotWidth + 'px'
                                    });
                                }

                                if (label.getBBox().height > this.len / tickPositions.length - (labelMetrics.h - labelMetrics.f)) {
                                    label.specCss = {
                                        textOverflow: 'ellipsis'
                                    };
                                }
                            }
                        }
                    }
                }


                // Add ellipsis if the label length is significantly longer than ideal
                if (attr.rotation) {
                    css = {
                        width: (maxLabelLength > chart.chartHeight * 0.5 ? chart.chartHeight * 0.33 : chart.chartHeight) + 'px'
                    };
                    if (!textOverflowOption) {
                        css.textOverflow = 'ellipsis';
                    }
                }

                // Set the explicit or automatic label alignment
                this.labelAlign = labelOptions.align || this.autoLabelAlign(this.labelRotation);
                if (this.labelAlign) {
                    attr.align = this.labelAlign;
                }

                // Apply general and specific CSS
                each(tickPositions, function(pos) {
                    var tick = ticks[pos],
                        label = tick && tick.label;
                    if (label) {
                        label.attr(attr); // This needs to go before the CSS in old IE (#4502)
                        if (css) {
                            label.css(merge(css, label.specCss));
                        }
                        delete label.specCss;
                        tick.rotation = attr.rotation;
                    }
                });

                // Note: Why is this not part of getLabelPosition?
                this.tickRotCorr = renderer.rotCorr(labelMetrics.b, this.labelRotation || 0, this.side !== 0);
            },

            /**
             * Return true if the axis has associated data
             */
            hasData: function() {
                return this.hasVisibleSeries || (defined(this.min) && defined(this.max) && !!this.tickPositions);
            },

            /**
             * Render the tick labels to a preliminary position to get their sizes
             */
            getOffset: function() {
                var axis = this,
                    chart = axis.chart,
                    renderer = chart.renderer,
                    options = axis.options,
                    tickPositions = axis.tickPositions,
                    ticks = axis.ticks,
                    horiz = axis.horiz,
                    side = axis.side,
                    invertedSide = chart.inverted ? [1, 0, 3, 2][side] : side,
                    hasData,
                    showAxis,
                    titleOffset = 0,
                    titleOffsetOption,
                    titleMargin = 0,
                    axisTitleOptions = options.title,
                    labelOptions = options.labels,
                    labelOffset = 0, // reset
                    labelOffsetPadded,
                    opposite = axis.opposite,
                    axisOffset = chart.axisOffset,
                    clipOffset = chart.clipOffset,
                    clip,
                    directionFactor = [-1, 1, 1, -1][side],
                    n,
                    className = options.className,
                    textAlign,
                    axisParent = axis.axisParent, // Used in color axis
                    lineHeightCorrection,
                    tickSize = this.tickSize('tick');

                // For reuse in Axis.render
                hasData = axis.hasData();
                axis.showAxis = showAxis = hasData || pick(options.showEmpty, true);

                // Set/reset staggerLines
                axis.staggerLines = axis.horiz && labelOptions.staggerLines;

                // Create the axisGroup and gridGroup elements on first iteration
                if (!axis.axisGroup) {
                    axis.gridGroup = renderer.g('grid')
                        .attr({
                            zIndex: options.gridZIndex || 1
                        })
                        .addClass('highcharts-' + this.coll.toLowerCase() + '-grid ' + (className || ''))
                        .add(axisParent);
                    axis.axisGroup = renderer.g('axis')
                        .attr({
                            zIndex: options.zIndex || 2
                        })
                        .addClass('highcharts-' + this.coll.toLowerCase() + ' ' + (className || ''))
                        .add(axisParent);
                    axis.labelGroup = renderer.g('axis-labels')
                        .attr({
                            zIndex: labelOptions.zIndex || 7
                        })
                        .addClass('highcharts-' + axis.coll.toLowerCase() + '-labels ' + (className || ''))
                        .add(axisParent);
                }

                if (hasData || axis.isLinked) {

                    // Generate ticks
                    each(tickPositions, function(pos) {
                        if (!ticks[pos]) {
                            ticks[pos] = new Tick(axis, pos);
                        } else {
                            ticks[pos].addLabel(); // update labels depending on tick interval
                        }
                    });

                    axis.renderUnsquish();


                    // Left side must be align: right and right side must have align: left for labels
                    if (labelOptions.reserveSpace !== false && (side === 0 || side === 2 || {
                            1: 'left',
                            3: 'right'
                        }[side] === axis.labelAlign || axis.labelAlign === 'center')) {
                        each(tickPositions, function(pos) {

                            // get the highest offset
                            labelOffset = Math.max(
                                ticks[pos].getLabelSize(),
                                labelOffset
                            );
                        });
                    }

                    if (axis.staggerLines) {
                        labelOffset *= axis.staggerLines;
                        axis.labelOffset = labelOffset * (axis.opposite ? -1 : 1);
                    }


                } else { // doesn't have data
                    for (n in ticks) {
                        ticks[n].destroy();
                        delete ticks[n];
                    }
                }

                if (axisTitleOptions && axisTitleOptions.text && axisTitleOptions.enabled !== false) {
                    if (!axis.axisTitle) {
                        textAlign = axisTitleOptions.textAlign;
                        if (!textAlign) {
                            textAlign = (horiz ? {
                                low: 'left',
                                middle: 'center',
                                high: 'right'
                            } : {
                                low: opposite ? 'right' : 'left',
                                middle: 'center',
                                high: opposite ? 'left' : 'right'
                            })[axisTitleOptions.align];
                        }
                        axis.axisTitle = renderer.text(
                                axisTitleOptions.text,
                                0,
                                0,
                                axisTitleOptions.useHTML
                            )
                            .attr({
                                zIndex: 7,
                                rotation: axisTitleOptions.rotation || 0,
                                align: textAlign
                            })
                            .addClass('highcharts-axis-title')

                        .css(axisTitleOptions.style)

                        .add(axis.axisGroup);
                        axis.axisTitle.isNew = true;
                    }

                    if (showAxis) {
                        titleOffset = axis.axisTitle.getBBox()[horiz ? 'height' : 'width'];
                        titleOffsetOption = axisTitleOptions.offset;
                        titleMargin = defined(titleOffsetOption) ? 0 : pick(axisTitleOptions.margin, horiz ? 5 : 10);
                    }

                    // hide or show the title depending on whether showEmpty is set
                    axis.axisTitle[showAxis ? 'show' : 'hide'](true);
                }

                // Render the axis line
                axis.renderLine();

                // handle automatic or user set offset
                axis.offset = directionFactor * pick(options.offset, axisOffset[side]);

                axis.tickRotCorr = axis.tickRotCorr || {
                    x: 0,
                    y: 0
                }; // polar
                if (side === 0) {
                    lineHeightCorrection = -axis.labelMetrics().h;
                } else if (side === 2) {
                    lineHeightCorrection = axis.tickRotCorr.y;
                } else {
                    lineHeightCorrection = 0;
                }

                // Find the padded label offset
                labelOffsetPadded = Math.abs(labelOffset) + titleMargin;
                if (labelOffset) {
                    labelOffsetPadded -= lineHeightCorrection;
                    labelOffsetPadded += directionFactor * (horiz ? pick(labelOptions.y, axis.tickRotCorr.y + directionFactor * 8) : labelOptions.x);
                }
                axis.axisTitleMargin = pick(titleOffsetOption, labelOffsetPadded);

                axisOffset[side] = Math.max(
                    axisOffset[side],
                    axis.axisTitleMargin + titleOffset + directionFactor * axis.offset,
                    labelOffsetPadded, // #3027
                    hasData && tickPositions.length && tickSize ? tickSize[0] : 0 // #4866
                );

                // Decide the clipping needed to keep the graph inside the plot area and axis lines
                clip = options.offset ? 0 : Math.floor(axis.axisLine.strokeWidth() / 2) * 2; // #4308, #4371
                clipOffset[invertedSide] = Math.max(clipOffset[invertedSide], clip);
            },

            /**
             * Get the path for the axis line
             */
            getLinePath: function(lineWidth) {
                var chart = this.chart,
                    opposite = this.opposite,
                    offset = this.offset,
                    horiz = this.horiz,
                    lineLeft = this.left + (opposite ? this.width : 0) + offset,
                    lineTop = chart.chartHeight - this.bottom - (opposite ? this.height : 0) + offset;

                if (opposite) {
                    lineWidth *= -1; // crispify the other way - #1480, #1687
                }

                return chart.renderer
                    .crispLine([
                        'M',
                        horiz ?
                        this.left :
                        lineLeft,
                        horiz ?
                        lineTop :
                        this.top,
                        'L',
                        horiz ?
                        chart.chartWidth - this.right :
                        lineLeft,
                        horiz ?
                        lineTop :
                        chart.chartHeight - this.bottom
                    ], lineWidth);
            },

            /**
             * Render the axis line
             */
            renderLine: function() {
                if (!this.axisLine) {
                    this.axisLine = this.chart.renderer.path()
                        .addClass('highcharts-axis-line')
                        .add(this.axisGroup);


                    this.axisLine.attr({
                        stroke: this.options.lineColor,
                        'stroke-width': this.options.lineWidth,
                        zIndex: 7
                    });

                }
            },

            /**
             * Position the title
             */
            getTitlePosition: function() {
                // compute anchor points for each of the title align options
                var horiz = this.horiz,
                    axisLeft = this.left,
                    axisTop = this.top,
                    axisLength = this.len,
                    axisTitleOptions = this.options.title,
                    margin = horiz ? axisLeft : axisTop,
                    opposite = this.opposite,
                    offset = this.offset,
                    xOption = axisTitleOptions.x || 0,
                    yOption = axisTitleOptions.y || 0,
                    fontSize = this.chart.renderer.fontMetrics(axisTitleOptions.style && axisTitleOptions.style.fontSize, this.axisTitle).f,

                    // the position in the length direction of the axis
                    alongAxis = {
                        low: margin + (horiz ? 0 : axisLength),
                        middle: margin + axisLength / 2,
                        high: margin + (horiz ? axisLength : 0)
                    }[axisTitleOptions.align],

                    // the position in the perpendicular direction of the axis
                    offAxis = (horiz ? axisTop + this.height : axisLeft) +
                    (horiz ? 1 : -1) * // horizontal axis reverses the margin
                    (opposite ? -1 : 1) * // so does opposite axes
                    this.axisTitleMargin +
                    (this.side === 2 ? fontSize : 0);

                return {
                    x: horiz ?
                        alongAxis + xOption : offAxis + (opposite ? this.width : 0) + offset + xOption,
                    y: horiz ?
                        offAxis + yOption - (opposite ? this.height : 0) + offset : alongAxis + yOption
                };
            },

            /**
             * Render the axis
             */
            render: function() {
                var axis = this,
                    chart = axis.chart,
                    renderer = chart.renderer,
                    options = axis.options,
                    isLog = axis.isLog,
                    lin2log = axis.lin2log,
                    isLinked = axis.isLinked,
                    tickPositions = axis.tickPositions,
                    axisTitle = axis.axisTitle,
                    ticks = axis.ticks,
                    minorTicks = axis.minorTicks,
                    alternateBands = axis.alternateBands,
                    stackLabelOptions = options.stackLabels,
                    alternateGridColor = options.alternateGridColor,
                    tickmarkOffset = axis.tickmarkOffset,
                    axisLine = axis.axisLine,
                    hasRendered = chart.hasRendered,
                    slideInTicks = hasRendered && isNumber(axis.oldMin),
                    showAxis = axis.showAxis,
                    animation = animObject(renderer.globalAnimation),
                    from,
                    to;

                // Reset
                axis.labelEdge.length = 0;
                //axis.justifyToPlot = overflow === 'justify';
                axis.overlap = false;

                // Mark all elements inActive before we go over and mark the active ones
                each([ticks, minorTicks, alternateBands], function(coll) {
                    var pos;
                    for (pos in coll) {
                        coll[pos].isActive = false;
                    }
                });

                // If the series has data draw the ticks. Else only the line and title
                if (axis.hasData() || isLinked) {

                    // minor ticks
                    if (axis.minorTickInterval && !axis.categories) {
                        each(axis.getMinorTickPositions(), function(pos) {
                            if (!minorTicks[pos]) {
                                minorTicks[pos] = new Tick(axis, pos, 'minor');
                            }

                            // render new ticks in old position
                            if (slideInTicks && minorTicks[pos].isNew) {
                                minorTicks[pos].render(null, true);
                            }

                            minorTicks[pos].render(null, false, 1);
                        });
                    }

                    // Major ticks. Pull out the first item and render it last so that
                    // we can get the position of the neighbour label. #808.
                    if (tickPositions.length) { // #1300
                        each(tickPositions, function(pos, i) {

                            // linked axes need an extra check to find out if
                            if (!isLinked || (pos >= axis.min && pos <= axis.max)) {

                                if (!ticks[pos]) {
                                    ticks[pos] = new Tick(axis, pos);
                                }

                                // render new ticks in old position
                                if (slideInTicks && ticks[pos].isNew) {
                                    ticks[pos].render(i, true, 0.1);
                                }

                                ticks[pos].render(i);
                            }

                        });
                        // In a categorized axis, the tick marks are displayed between labels. So
                        // we need to add a tick mark and grid line at the left edge of the X axis.
                        if (tickmarkOffset && (axis.min === 0 || axis.single)) {
                            if (!ticks[-1]) {
                                ticks[-1] = new Tick(axis, -1, null, true);
                            }
                            ticks[-1].render(-1);
                        }

                    }

                    // alternate grid color
                    if (alternateGridColor) {
                        each(tickPositions, function(pos, i) {
                            to = tickPositions[i + 1] !== undefined ? tickPositions[i + 1] + tickmarkOffset : axis.max - tickmarkOffset;
                            if (i % 2 === 0 && pos < axis.max && to <= axis.max + (chart.polar ? -tickmarkOffset : tickmarkOffset)) { // #2248, #4660
                                if (!alternateBands[pos]) {
                                    alternateBands[pos] = new PlotLineOrBand(axis);
                                }
                                from = pos + tickmarkOffset; // #949
                                alternateBands[pos].options = {
                                    from: isLog ? lin2log(from) : from,
                                    to: isLog ? lin2log(to) : to,
                                    color: alternateGridColor
                                };
                                alternateBands[pos].render();
                                alternateBands[pos].isActive = true;
                            }
                        });
                    }

                    // custom plot lines and bands
                    if (!axis._addedPlotLB) { // only first time
                        each((options.plotLines || []).concat(options.plotBands || []), function(plotLineOptions) {
                            axis.addPlotBandOrLine(plotLineOptions);
                        });
                        axis._addedPlotLB = true;
                    }

                } // end if hasData

                // Remove inactive ticks
                each([ticks, minorTicks, alternateBands], function(coll) {
                    var pos,
                        i,
                        forDestruction = [],
                        delay = animation.duration,
                        destroyInactiveItems = function() {
                            i = forDestruction.length;
                            while (i--) {
                                // When resizing rapidly, the same items may be destroyed in different timeouts,
                                // or the may be reactivated
                                if (coll[forDestruction[i]] && !coll[forDestruction[i]].isActive) {
                                    coll[forDestruction[i]].destroy();
                                    delete coll[forDestruction[i]];
                                }
                            }

                        };

                    for (pos in coll) {

                        if (!coll[pos].isActive) {
                            // Render to zero opacity
                            coll[pos].render(pos, false, 0);
                            coll[pos].isActive = false;
                            forDestruction.push(pos);
                        }
                    }

                    // When the objects are finished fading out, destroy them
                    syncTimeout(
                        destroyInactiveItems,
                        coll === alternateBands || !chart.hasRendered || !delay ? 0 : delay
                    );
                });

                // Set the axis line path
                if (axisLine) {
                    axisLine[axisLine.isPlaced ? 'animate' : 'attr']({
                        d: this.getLinePath(axisLine.strokeWidth())
                    });
                    axisLine.isPlaced = true;

                    // Show or hide the line depending on options.showEmpty
                    axisLine[showAxis ? 'show' : 'hide'](true);
                }

                if (axisTitle && showAxis) {

                    axisTitle[axisTitle.isNew ? 'attr' : 'animate'](
                        axis.getTitlePosition()
                    );
                    axisTitle.isNew = false;
                }

                // Stacked totals:
                if (stackLabelOptions && stackLabelOptions.enabled) {
                    axis.renderStackTotals();
                }
                // End stacked totals

                axis.isDirty = false;
            },

            /**
             * Redraw the axis to reflect changes in the data or axis extremes
             */
            redraw: function() {

                if (this.visible) {
                    // render the axis
                    this.render();

                    // move plot lines and bands
                    each(this.plotLinesAndBands, function(plotLine) {
                        plotLine.render();
                    });
                }

                // mark associated series as dirty and ready for redraw
                each(this.series, function(series) {
                    series.isDirty = true;
                });

            },

            // Properties to survive after destroy, needed for Axis.update (#4317,
            // #5773, #5881).
            keepProps: ['extKey', 'hcEvents', 'names', 'series', 'userMax', 'userMin'],

            /**
             * Destroys an Axis instance.
             */
            destroy: function(keepEvents) {
                var axis = this,
                    stacks = axis.stacks,
                    stackKey,
                    plotLinesAndBands = axis.plotLinesAndBands,
                    i,
                    n;

                // Remove the events
                if (!keepEvents) {
                    removeEvent(axis);
                }

                // Destroy each stack total
                for (stackKey in stacks) {
                    destroyObjectProperties(stacks[stackKey]);

                    stacks[stackKey] = null;
                }

                // Destroy collections
                each([axis.ticks, axis.minorTicks, axis.alternateBands], function(coll) {
                    destroyObjectProperties(coll);
                });
                if (plotLinesAndBands) {
                    i = plotLinesAndBands.length;
                    while (i--) { // #1975
                        plotLinesAndBands[i].destroy();
                    }
                }

                // Destroy local variables
                each(['stackTotalGroup', 'axisLine', 'axisTitle', 'axisGroup', 'gridGroup', 'labelGroup', 'cross'], function(prop) {
                    if (axis[prop]) {
                        axis[prop] = axis[prop].destroy();
                    }
                });

                // Delete all properties and fall back to the prototype.
                for (n in axis) {
                    if (axis.hasOwnProperty(n) && inArray(n, axis.keepProps) === -1) {
                        delete axis[n];
                    }
                }
            },

            /**
             * Draw the crosshair
             * 
             * @param  {Object} e The event arguments from the modified pointer event
             * @param  {Object} point The Point object
             */
            drawCrosshair: function(e, point) {

                var path,
                    options = this.crosshair,
                    snap = pick(options.snap, true),
                    pos,
                    categorized,
                    graphic = this.cross;

                // Use last available event when updating non-snapped crosshairs without
                // mouse interaction (#5287)
                if (!e) {
                    e = this.cross && this.cross.e;
                }

                if (
                    // Disabled in options
                    !this.crosshair ||
                    // Snap
                    ((defined(point) || !snap) === false)
                ) {
                    this.hideCrosshair();
                } else {

                    // Get the path
                    if (!snap) {
                        pos = e && (this.horiz ? e.chartX - this.pos : this.len - e.chartY + this.pos);
                    } else if (defined(point)) {
                        pos = this.isXAxis ? point.plotX : this.len - point.plotY; // #3834
                    }

                    if (defined(pos)) {
                        path = this.getPlotLinePath(
                            // First argument, value, only used on radial
                            point && (this.isXAxis ? point.x : pick(point.stackY, point.y)),
                            null,
                            null,
                            null,
                            pos // Translated position
                        ) || null; // #3189
                    }

                    if (!defined(path)) {
                        this.hideCrosshair();
                        return;
                    }

                    categorized = this.categories && !this.isRadial;

                    // Draw the cross
                    if (!graphic) {
                        this.cross = graphic = this.chart.renderer
                            .path()
                            .addClass('highcharts-crosshair highcharts-crosshair-' +
                                (categorized ? 'category ' : 'thin ') + options.className)
                            .attr({
                                zIndex: pick(options.zIndex, 2)
                            })
                            .add();


                        // Presentational attributes
                        graphic.attr({
                            'stroke': options.color || (categorized ? color('#ccd6eb').setOpacity(0.25).get() : '#cccccc'),
                            'stroke-width': pick(options.width, 1)
                        });
                        if (options.dashStyle) {
                            graphic.attr({
                                dashstyle: options.dashStyle
                            });
                        }


                    }

                    graphic.show().attr({
                        d: path
                    });

                    if (categorized && !options.width) {
                        graphic.attr({
                            'stroke-width': this.transA
                        });
                    }
                    this.cross.e = e;
                }
            },

            /**
             *	Hide the crosshair.
             */
            hideCrosshair: function() {
                if (this.cross) {
                    this.cross.hide();
                }
            }
        }; // end Axis

        extend(H.Axis.prototype, AxisPlotLineOrBandExtension);

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var Axis = H.Axis,
            Date = H.Date,
            dateFormat = H.dateFormat,
            defaultOptions = H.defaultOptions,
            defined = H.defined,
            each = H.each,
            extend = H.extend,
            getMagnitude = H.getMagnitude,
            getTZOffset = H.getTZOffset,
            normalizeTickInterval = H.normalizeTickInterval,
            pick = H.pick,
            timeUnits = H.timeUnits;
        /**
         * Set the tick positions to a time unit that makes sense, for example
         * on the first of each month or on every Monday. Return an array
         * with the time positions. Used in datetime axes as well as for grouping
         * data on a datetime axis.
         *
         * @param {Object} normalizedInterval The interval in axis values (ms) and the count
         * @param {Number} min The minimum in axis values
         * @param {Number} max The maximum in axis values
         * @param {Number} startOfWeek
         */
        Axis.prototype.getTimeTicks = function(normalizedInterval, min, max, startOfWeek) {
            var tickPositions = [],
                i,
                higherRanks = {},
                useUTC = defaultOptions.global.useUTC,
                minYear, // used in months and years as a basis for Date.UTC()
                minDate = new Date(min - getTZOffset(min)),
                makeTime = Date.hcMakeTime,
                interval = normalizedInterval.unitRange,
                count = normalizedInterval.count,
                variableDayLength;

            if (defined(min)) { // #1300
                minDate[Date.hcSetMilliseconds](interval >= timeUnits.second ? 0 : // #3935
                    count * Math.floor(minDate.getMilliseconds() / count)); // #3652, #3654

                if (interval >= timeUnits.second) { // second
                    minDate[Date.hcSetSeconds](interval >= timeUnits.minute ? 0 : // #3935
                        count * Math.floor(minDate.getSeconds() / count));
                }

                if (interval >= timeUnits.minute) { // minute
                    minDate[Date.hcSetMinutes](interval >= timeUnits.hour ? 0 :
                        count * Math.floor(minDate[Date.hcGetMinutes]() / count));
                }

                if (interval >= timeUnits.hour) { // hour
                    minDate[Date.hcSetHours](interval >= timeUnits.day ? 0 :
                        count * Math.floor(minDate[Date.hcGetHours]() / count));
                }

                if (interval >= timeUnits.day) { // day
                    minDate[Date.hcSetDate](interval >= timeUnits.month ? 1 :
                        count * Math.floor(minDate[Date.hcGetDate]() / count));
                }

                if (interval >= timeUnits.month) { // month
                    minDate[Date.hcSetMonth](interval >= timeUnits.year ? 0 :
                        count * Math.floor(minDate[Date.hcGetMonth]() / count));
                    minYear = minDate[Date.hcGetFullYear]();
                }

                if (interval >= timeUnits.year) { // year
                    minYear -= minYear % count;
                    minDate[Date.hcSetFullYear](minYear);
                }

                // week is a special case that runs outside the hierarchy
                if (interval === timeUnits.week) {
                    // get start of current week, independent of count
                    minDate[Date.hcSetDate](minDate[Date.hcGetDate]() - minDate[Date.hcGetDay]() +
                        pick(startOfWeek, 1));
                }


                // Get basics for variable time spans
                minYear = minDate[Date.hcGetFullYear]();
                var minMonth = minDate[Date.hcGetMonth](),
                    minDateDate = minDate[Date.hcGetDate](),
                    minHours = minDate[Date.hcGetHours]();


                // Handle local timezone offset
                if (Date.hcTimezoneOffset || Date.hcGetTimezoneOffset) {

                    // Detect whether we need to take the DST crossover into
                    // consideration. If we're crossing over DST, the day length may be
                    // 23h or 25h and we need to compute the exact clock time for each
                    // tick instead of just adding hours. This comes at a cost, so first
                    // we found out if it is needed. #4951.
                    variableDayLength =
                        (!useUTC || !!Date.hcGetTimezoneOffset) &&
                        (
                            // Long range, assume we're crossing over.
                            max - min > 4 * timeUnits.month ||
                            // Short range, check if min and max are in different time 
                            // zones.
                            getTZOffset(min) !== getTZOffset(max)
                        );

                    // Adjust minDate to the offset date
                    minDate = minDate.getTime();
                    minDate = new Date(minDate + getTZOffset(minDate));
                }


                // Iterate and add tick positions at appropriate values
                var time = minDate.getTime();
                i = 1;
                while (time < max) {
                    tickPositions.push(time);

                    // if the interval is years, use Date.UTC to increase years
                    if (interval === timeUnits.year) {
                        time = makeTime(minYear + i * count, 0);

                        // if the interval is months, use Date.UTC to increase months
                    } else if (interval === timeUnits.month) {
                        time = makeTime(minYear, minMonth + i * count);

                        // if we're using global time, the interval is not fixed as it jumps
                        // one hour at the DST crossover
                    } else if (variableDayLength && (interval === timeUnits.day || interval === timeUnits.week)) {
                        time = makeTime(minYear, minMonth, minDateDate +
                            i * count * (interval === timeUnits.day ? 1 : 7));

                    } else if (variableDayLength && interval === timeUnits.hour) {
                        time = makeTime(minYear, minMonth, minDateDate, minHours + i * count);

                        // else, the interval is fixed and we use simple addition
                    } else {
                        time += interval * count;
                    }

                    i++;
                }

                // push the last time
                tickPositions.push(time);


                // Handle higher ranks. Mark new days if the time is on midnight
                // (#950, #1649, #1760, #3349)
                if (interval <= timeUnits.hour) {
                    each(tickPositions, function(time) {
                        if (dateFormat('%H%M%S%L', time) === '000000000') {
                            higherRanks[time] = 'day';
                        }
                    });
                }
            }


            // record information on the chosen unit - for dynamic label formatter
            tickPositions.info = extend(normalizedInterval, {
                higherRanks: higherRanks,
                totalRange: interval * count
            });

            return tickPositions;
        };

        /**
         * Get a normalized tick interval for dates. Returns a configuration object with
         * unit range (interval), count and name. Used to prepare data for getTimeTicks.
         * Previously this logic was part of getTimeTicks, but as getTimeTicks now runs
         * of segments in stock charts, the normalizing logic was extracted in order to
         * prevent it for running over again for each segment having the same interval.
         * #662, #697.
         */
        Axis.prototype.normalizeTimeTickInterval = function(tickInterval, unitsOption) {
            var units = unitsOption || [
                    [
                        'millisecond', // unit name
                        [1, 2, 5, 10, 20, 25, 50, 100, 200, 500] // allowed multiples
                    ],
                    [
                        'second', [1, 2, 5, 10, 15, 30]
                    ],
                    [
                        'minute', [1, 2, 5, 10, 15, 30]
                    ],
                    [
                        'hour', [1, 2, 3, 4, 6, 8, 12]
                    ],
                    [
                        'day', [1, 2]
                    ],
                    [
                        'week', [1, 2]
                    ],
                    [
                        'month', [1, 2, 3, 4, 6]
                    ],
                    [
                        'year',
                        null
                    ]
                ],
                unit = units[units.length - 1], // default unit is years
                interval = timeUnits[unit[0]],
                multiples = unit[1],
                count,
                i;

            // loop through the units to find the one that best fits the tickInterval
            for (i = 0; i < units.length; i++) {
                unit = units[i];
                interval = timeUnits[unit[0]];
                multiples = unit[1];


                if (units[i + 1]) {
                    // lessThan is in the middle between the highest multiple and the next unit.
                    var lessThan = (interval * multiples[multiples.length - 1] +
                        timeUnits[units[i + 1][0]]) / 2;

                    // break and keep the current unit
                    if (tickInterval <= lessThan) {
                        break;
                    }
                }
            }

            // prevent 2.5 years intervals, though 25, 250 etc. are allowed
            if (interval === timeUnits.year && tickInterval < 5 * interval) {
                multiples = [1, 2, 5];
            }

            // get the count
            count = normalizeTickInterval(
                tickInterval / interval,
                multiples,
                unit[0] === 'year' ? Math.max(getMagnitude(tickInterval / interval), 1) : 1 // #1913, #2360
            );

            return {
                unitRange: interval,
                count: count,
                unitName: unit[0]
            };
        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var Axis = H.Axis,
            getMagnitude = H.getMagnitude,
            map = H.map,
            normalizeTickInterval = H.normalizeTickInterval,
            pick = H.pick;
        /**
         * Methods defined on the Axis prototype
         */

        /**
         * Set the tick positions of a logarithmic axis
         */
        Axis.prototype.getLogTickPositions = function(interval, min, max, minor) {
            var axis = this,
                options = axis.options,
                axisLength = axis.len,
                lin2log = axis.lin2log,
                log2lin = axis.log2lin,
                // Since we use this method for both major and minor ticks,
                // use a local variable and return the result
                positions = [];

            // Reset
            if (!minor) {
                axis._minorAutoInterval = null;
            }

            // First case: All ticks fall on whole logarithms: 1, 10, 100 etc.
            if (interval >= 0.5) {
                interval = Math.round(interval);
                positions = axis.getLinearTickPositions(interval, min, max);

                // Second case: We need intermediary ticks. For example
                // 1, 2, 4, 6, 8, 10, 20, 40 etc.
            } else if (interval >= 0.08) {
                var roundedMin = Math.floor(min),
                    intermediate,
                    i,
                    j,
                    len,
                    pos,
                    lastPos,
                    break2;

                if (interval > 0.3) {
                    intermediate = [1, 2, 4];
                } else if (interval > 0.15) { // 0.2 equals five minor ticks per 1, 10, 100 etc
                    intermediate = [1, 2, 4, 6, 8];
                } else { // 0.1 equals ten minor ticks per 1, 10, 100 etc
                    intermediate = [1, 2, 3, 4, 5, 6, 7, 8, 9];
                }

                for (i = roundedMin; i < max + 1 && !break2; i++) {
                    len = intermediate.length;
                    for (j = 0; j < len && !break2; j++) {
                        pos = log2lin(lin2log(i) * intermediate[j]);
                        if (pos > min && (!minor || lastPos <= max) && lastPos !== undefined) { // #1670, lastPos is #3113
                            positions.push(lastPos);
                        }

                        if (lastPos > max) {
                            break2 = true;
                        }
                        lastPos = pos;
                    }
                }

                // Third case: We are so deep in between whole logarithmic values that
                // we might as well handle the tick positions like a linear axis. For
                // example 1.01, 1.02, 1.03, 1.04.
            } else {
                var realMin = lin2log(min),
                    realMax = lin2log(max),
                    tickIntervalOption = options[minor ? 'minorTickInterval' : 'tickInterval'],
                    filteredTickIntervalOption = tickIntervalOption === 'auto' ? null : tickIntervalOption,
                    tickPixelIntervalOption = options.tickPixelInterval / (minor ? 5 : 1),
                    totalPixelLength = minor ? axisLength / axis.tickPositions.length : axisLength;

                interval = pick(
                    filteredTickIntervalOption,
                    axis._minorAutoInterval,
                    (realMax - realMin) * tickPixelIntervalOption / (totalPixelLength || 1)
                );

                interval = normalizeTickInterval(
                    interval,
                    null,
                    getMagnitude(interval)
                );

                positions = map(axis.getLinearTickPositions(
                    interval,
                    realMin,
                    realMax
                ), log2lin);

                if (!minor) {
                    axis._minorAutoInterval = interval / 5;
                }
            }

            // Set the axis-level tickInterval variable
            if (!minor) {
                axis.tickInterval = interval;
            }
            return positions;
        };

        Axis.prototype.log2lin = function(num) {
            return Math.log(num) / Math.LN10;
        };

        Axis.prototype.lin2log = function(num) {
            return Math.pow(10, num);
        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var dateFormat = H.dateFormat,
            each = H.each,
            extend = H.extend,
            format = H.format,
            isNumber = H.isNumber,
            map = H.map,
            merge = H.merge,
            pick = H.pick,
            splat = H.splat,
            syncTimeout = H.syncTimeout,
            timeUnits = H.timeUnits;
        /**
         * The tooltip object
         * @param {Object} chart The chart instance
         * @param {Object} options Tooltip options
         */
        H.Tooltip = function() {
            this.init.apply(this, arguments);
        };

        H.Tooltip.prototype = {

            init: function(chart, options) {

                // Save the chart and options
                this.chart = chart;
                this.options = options;

                // Keep track of the current series
                //this.currentSeries = undefined;

                // List of crosshairs
                this.crosshairs = [];

                // Current values of x and y when animating
                this.now = {
                    x: 0,
                    y: 0
                };

                // The tooltip is initially hidden
                this.isHidden = true;



                // Public property for getting the shared state.
                this.split = options.split && !chart.inverted;
                this.shared = options.shared || this.split;

            },

            /**
             * Destroy the single tooltips in a split tooltip.
             * If the tooltip is active then it is not destroyed, unless forced to.
             * @param  {boolean} force Force destroy all tooltips.
             * @return {undefined}
             */
            cleanSplit: function(force) {
                each(this.chart.series, function(series) {
                    var tt = series && series.tt;
                    if (tt) {
                        if (!tt.isActive || force) {
                            series.tt = tt.destroy();
                        } else {
                            tt.isActive = false;
                        }
                    }
                });
            },




            /**
             * Create the Tooltip label element if it doesn't exist, then return the
             * label.
             */
            getLabel: function() {

                var renderer = this.chart.renderer,
                    options = this.options;

                if (!this.label) {
                    // Create the label
                    if (this.split) {
                        this.label = renderer.g('tooltip');
                    } else {
                        this.label = renderer.label(
                                '',
                                0,
                                0,
                                options.shape || 'callout',
                                null,
                                null,
                                options.useHTML,
                                null,
                                'tooltip'
                            )
                            .attr({
                                padding: options.padding,
                                r: options.borderRadius
                            });


                        this.label
                            .attr({
                                'fill': options.backgroundColor,
                                'stroke-width': options.borderWidth
                            })
                            // #2301, #2657
                            .css(options.style)
                            .shadow(options.shadow);

                    }



                    this.label
                        .attr({
                            zIndex: 8
                        })
                        .add();
                }
                return this.label;
            },

            update: function(options) {
                this.destroy();
                this.init(this.chart, merge(true, this.options, options));
            },

            /**
             * Destroy the tooltip and its elements.
             */
            destroy: function() {
                // Destroy and clear local variables
                if (this.label) {
                    this.label = this.label.destroy();
                }
                if (this.split && this.tt) {
                    this.cleanSplit(this.chart, true);
                    this.tt = this.tt.destroy();
                }
                clearTimeout(this.hideTimer);
                clearTimeout(this.tooltipTimeout);
            },

            /**
             * Provide a soft movement for the tooltip
             *
             * @param {Number} x
             * @param {Number} y
             * @private
             */
            move: function(x, y, anchorX, anchorY) {
                var tooltip = this,
                    now = tooltip.now,
                    animate = tooltip.options.animation !== false && !tooltip.isHidden &&
                    // When we get close to the target position, abort animation and land on the right place (#3056)
                    (Math.abs(x - now.x) > 1 || Math.abs(y - now.y) > 1),
                    skipAnchor = tooltip.followPointer || tooltip.len > 1;

                // Get intermediate values for animation
                extend(now, {
                    x: animate ? (2 * now.x + x) / 3 : x,
                    y: animate ? (now.y + y) / 2 : y,
                    anchorX: skipAnchor ? undefined : animate ? (2 * now.anchorX + anchorX) / 3 : anchorX,
                    anchorY: skipAnchor ? undefined : animate ? (now.anchorY + anchorY) / 2 : anchorY
                });

                // Move to the intermediate value
                tooltip.getLabel().attr(now);


                // Run on next tick of the mouse tracker
                if (animate) {

                    // Never allow two timeouts
                    clearTimeout(this.tooltipTimeout);

                    // Set the fixed interval ticking for the smooth tooltip
                    this.tooltipTimeout = setTimeout(function() {
                        // The interval function may still be running during destroy,
                        // so check that the chart is really there before calling.
                        if (tooltip) {
                            tooltip.move(x, y, anchorX, anchorY);
                        }
                    }, 32);

                }
            },

            /**
             * Hide the tooltip
             */
            hide: function(delay) {
                var tooltip = this;
                clearTimeout(this.hideTimer); // disallow duplicate timers (#1728, #1766)
                delay = pick(delay, this.options.hideDelay, 500);
                if (!this.isHidden) {
                    this.hideTimer = syncTimeout(function() {
                        tooltip.getLabel()[delay ? 'fadeOut' : 'hide']();
                        tooltip.isHidden = true;
                    }, delay);
                }
            },

            /**
             * Extendable method to get the anchor position of the tooltip
             * from a point or set of points
             */
            getAnchor: function(points, mouseEvent) {
                var ret,
                    chart = this.chart,
                    inverted = chart.inverted,
                    plotTop = chart.plotTop,
                    plotLeft = chart.plotLeft,
                    plotX = 0,
                    plotY = 0,
                    yAxis,
                    xAxis;

                points = splat(points);

                // Pie uses a special tooltipPos
                ret = points[0].tooltipPos;

                // When tooltip follows mouse, relate the position to the mouse
                if (this.followPointer && mouseEvent) {
                    if (mouseEvent.chartX === undefined) {
                        mouseEvent = chart.pointer.normalize(mouseEvent);
                    }
                    ret = [
                        mouseEvent.chartX - chart.plotLeft,
                        mouseEvent.chartY - plotTop
                    ];
                }
                // When shared, use the average position
                if (!ret) {
                    each(points, function(point) {
                        yAxis = point.series.yAxis;
                        xAxis = point.series.xAxis;
                        plotX += point.plotX + (!inverted && xAxis ? xAxis.left - plotLeft : 0);
                        plotY += (point.plotLow ? (point.plotLow + point.plotHigh) / 2 : point.plotY) +
                            (!inverted && yAxis ? yAxis.top - plotTop : 0); // #1151
                    });

                    plotX /= points.length;
                    plotY /= points.length;

                    ret = [
                        inverted ? chart.plotWidth - plotY : plotX,
                        this.shared && !inverted && points.length > 1 && mouseEvent ?
                        mouseEvent.chartY - plotTop : // place shared tooltip next to the mouse (#424)
                        inverted ? chart.plotHeight - plotX : plotY
                    ];
                }

                return map(ret, Math.round);
            },

            /**
             * Place the tooltip in a chart without spilling over
             * and not covering the point it self.
             */
            getPosition: function(boxWidth, boxHeight, point) {

                var chart = this.chart,
                    distance = this.distance,
                    ret = {},
                    h = point.h || 0, // #4117
                    swapped,
                    first = ['y', chart.chartHeight, boxHeight,
                        point.plotY + chart.plotTop, chart.plotTop,
                        chart.plotTop + chart.plotHeight
                    ],
                    second = ['x', chart.chartWidth, boxWidth,
                        point.plotX + chart.plotLeft, chart.plotLeft,
                        chart.plotLeft + chart.plotWidth
                    ],
                    // The far side is right or bottom
                    preferFarSide = !this.followPointer && pick(point.ttBelow, !chart.inverted === !!point.negative), // #4984
                    /**
                     * Handle the preferred dimension. When the preferred dimension is tooltip
                     * on top or bottom of the point, it will look for space there.
                     */
                    firstDimension = function(dim, outerSize, innerSize, point, min, max) {
                        var roomLeft = innerSize < point - distance,
                            roomRight = point + distance + innerSize < outerSize,
                            alignedLeft = point - distance - innerSize,
                            alignedRight = point + distance;

                        if (preferFarSide && roomRight) {
                            ret[dim] = alignedRight;
                        } else if (!preferFarSide && roomLeft) {
                            ret[dim] = alignedLeft;
                        } else if (roomLeft) {
                            ret[dim] = Math.min(max - innerSize, alignedLeft - h < 0 ? alignedLeft : alignedLeft - h);
                        } else if (roomRight) {
                            ret[dim] = Math.max(
                                min,
                                alignedRight + h + innerSize > outerSize ?
                                alignedRight :
                                alignedRight + h
                            );
                        } else {
                            return false;
                        }
                    },
                    /**
                     * Handle the secondary dimension. If the preferred dimension is tooltip
                     * on top or bottom of the point, the second dimension is to align the tooltip
                     * above the point, trying to align center but allowing left or right
                     * align within the chart box.
                     */
                    secondDimension = function(dim, outerSize, innerSize, point) {
                        var retVal;

                        // Too close to the edge, return false and swap dimensions
                        if (point < distance || point > outerSize - distance) {
                            retVal = false;
                            // Align left/top
                        } else if (point < innerSize / 2) {
                            ret[dim] = 1;
                            // Align right/bottom
                        } else if (point > outerSize - innerSize / 2) {
                            ret[dim] = outerSize - innerSize - 2;
                            // Align center
                        } else {
                            ret[dim] = point - innerSize / 2;
                        }
                        return retVal;
                    },
                    /**
                     * Swap the dimensions
                     */
                    swap = function(count) {
                        var temp = first;
                        first = second;
                        second = temp;
                        swapped = count;
                    },
                    run = function() {
                        if (firstDimension.apply(0, first) !== false) {
                            if (secondDimension.apply(0, second) === false && !swapped) {
                                swap(true);
                                run();
                            }
                        } else if (!swapped) {
                            swap(true);
                            run();
                        } else {
                            ret.x = ret.y = 0;
                        }
                    };

                // Under these conditions, prefer the tooltip on the side of the point
                if (chart.inverted || this.len > 1) {
                    swap();
                }
                run();

                return ret;

            },

            /**
             * In case no user defined formatter is given, this will be used. Note that the context
             * here is an object holding point, series, x, y etc.
             *
             * @returns {String|Array<String>}
             */
            defaultFormatter: function(tooltip) {
                var items = this.points || splat(this),
                    s;

                // Build the header
                s = [tooltip.tooltipFooterHeaderFormatter(items[0])];

                // build the values
                s = s.concat(tooltip.bodyFormatter(items));

                // footer
                s.push(tooltip.tooltipFooterHeaderFormatter(items[0], true));

                return s;
            },

            /**
             * Refresh the tooltip's text and position.
             * @param {Object} point
             */
            refresh: function(point, mouseEvent) {
                var tooltip = this,
                    chart = tooltip.chart,
                    label,
                    options = tooltip.options,
                    x,
                    y,
                    anchor,
                    textConfig = {},
                    text,
                    pointConfig = [],
                    formatter = options.formatter || tooltip.defaultFormatter,
                    hoverPoints = chart.hoverPoints,
                    shared = tooltip.shared,
                    currentSeries;

                clearTimeout(this.hideTimer);

                // get the reference point coordinates (pie charts use tooltipPos)
                tooltip.followPointer = splat(point)[0].series.tooltipOptions.followPointer;
                anchor = tooltip.getAnchor(point, mouseEvent);
                x = anchor[0];
                y = anchor[1];

                // shared tooltip, array is sent over
                if (shared && !(point.series && point.series.noSharedTooltip)) {

                    // hide previous hoverPoints and set new

                    chart.hoverPoints = point;
                    if (hoverPoints) {
                        each(hoverPoints, function(point) {
                            point.setState();
                        });
                    }

                    each(point, function(item) {
                        item.setState('hover');

                        pointConfig.push(item.getLabelConfig());
                    });

                    textConfig = {
                        x: point[0].category,
                        y: point[0].y
                    };
                    textConfig.points = pointConfig;
                    this.len = pointConfig.length;
                    point = point[0];

                    // single point tooltip
                } else {
                    textConfig = point.getLabelConfig();
                }
                text = formatter.call(textConfig, tooltip);

                // register the current series
                currentSeries = point.series;
                this.distance = pick(currentSeries.tooltipOptions.distance, 16);

                // update the inner HTML
                if (text === false) {
                    this.hide();
                } else {

                    label = tooltip.getLabel();

                    // show it
                    if (tooltip.isHidden) {
                        label.attr({
                            opacity: 1
                        }).show();
                    }

                    // update text
                    if (tooltip.split) {
                        this.renderSplit(text, chart.hoverPoints);
                    } else {
                        label.attr({
                            text: text && text.join ? text.join('') : text
                        });

                        // Set the stroke color of the box to reflect the point
                        label.removeClass(/highcharts-color-[\d]+/g)
                            .addClass('highcharts-color-' + pick(point.colorIndex, currentSeries.colorIndex));


                        label.attr({
                            stroke: options.borderColor || point.color || currentSeries.color || '#666666'
                        });


                        tooltip.updatePosition({
                            plotX: x,
                            plotY: y,
                            negative: point.negative,
                            ttBelow: point.ttBelow,
                            h: anchor[2] || 0
                        });
                    }

                    this.isHidden = false;
                }
            },

            /**
             * Render the split tooltip. Loops over each point's text and adds
             * a label next to the point, then uses the distribute function to 
             * find best non-overlapping positions.
             */
            renderSplit: function(labels, points) {
                var tooltip = this,
                    boxes = [],
                    chart = this.chart,
                    ren = chart.renderer,
                    rightAligned = true,
                    options = this.options,
                    headerHeight,
                    tooltipLabel = this.getLabel();

                // Create the individual labels
                each(labels.slice(0, labels.length - 1), function(str, i) {
                    var point = points[i - 1] ||
                        // Item 0 is the header. Instead of this, we could also use the crosshair label
                        {
                            isHeader: true,
                            plotX: points[0].plotX
                        },
                        owner = point.series || tooltip,
                        tt = owner.tt,
                        series = point.series || {},
                        colorClass = 'highcharts-color-' + pick(point.colorIndex, series.colorIndex, 'none'),
                        target,
                        x,
                        bBox,
                        boxWidth;

                    // Store the tooltip referance on the series
                    if (!tt) {
                        owner.tt = tt = ren.label(null, null, null, 'callout')
                            .addClass('highcharts-tooltip-box ' + colorClass)
                            .attr({
                                'padding': options.padding,
                                'r': options.borderRadius,

                                'fill': options.backgroundColor,
                                'stroke': point.color || series.color || '#333333',
                                'stroke-width': options.borderWidth

                            })
                            .add(tooltipLabel);
                    }

                    tt.isActive = true;
                    tt.attr({
                        text: str
                    });

                    tt.css(options.style);


                    // Get X position now, so we can move all to the other side in case of overflow
                    bBox = tt.getBBox();
                    boxWidth = bBox.width + tt.strokeWidth();
                    if (point.isHeader) {
                        headerHeight = bBox.height;
                        x = Math.max(
                            0, // No left overflow
                            Math.min(
                                point.plotX + chart.plotLeft - boxWidth / 2,
                                chart.chartWidth - boxWidth // No right overflow (#5794)
                            )
                        );
                    } else {
                        x = point.plotX + chart.plotLeft - pick(options.distance, 16) -
                            boxWidth;
                    }


                    // If overflow left, we don't use this x in the next loop
                    if (x < 0) {
                        rightAligned = false;
                    }

                    // Prepare for distribution
                    target = (point.series && point.series.yAxis && point.series.yAxis.pos) + (point.plotY || 0);
                    target -= chart.plotTop;
                    boxes.push({
                        target: point.isHeader ? chart.plotHeight + headerHeight : target,
                        rank: point.isHeader ? 1 : 0,
                        size: owner.tt.getBBox().height + 1,
                        point: point,
                        x: x,
                        tt: tt
                    });
                });

                // Clean previous run (for missing points)
                this.cleanSplit();

                // Distribute and put in place
                H.distribute(boxes, chart.plotHeight + headerHeight);
                each(boxes, function(box) {
                    var point = box.point;

                    // Put the label in place
                    box.tt.attr({
                        visibility: box.pos === undefined ? 'hidden' : 'inherit',
                        x: (rightAligned || point.isHeader ?
                            box.x :
                            point.plotX + chart.plotLeft + pick(options.distance, 16)),
                        y: box.pos + chart.plotTop,
                        anchorX: point.plotX + chart.plotLeft,
                        anchorY: point.isHeader ?
                            box.pos + chart.plotTop - 15 : point.plotY + chart.plotTop
                    });
                });
            },

            /**
             * Find the new position and perform the move
             */
            updatePosition: function(point) {
                var chart = this.chart,
                    label = this.getLabel(),
                    pos = (this.options.positioner || this.getPosition).call(
                        this,
                        label.width,
                        label.height,
                        point
                    );

                // do the move
                this.move(
                    Math.round(pos.x),
                    Math.round(pos.y || 0), // can be undefined (#3977) 
                    point.plotX + chart.plotLeft,
                    point.plotY + chart.plotTop
                );
            },

            /**
             * Get the best X date format based on the closest point range on the axis.
             */
            getXDateFormat: function(point, options, xAxis) {
                var xDateFormat,
                    dateTimeLabelFormats = options.dateTimeLabelFormats,
                    closestPointRange = xAxis && xAxis.closestPointRange,
                    n,
                    blank = '01-01 00:00:00.000',
                    strpos = {
                        millisecond: 15,
                        second: 12,
                        minute: 9,
                        hour: 6,
                        day: 3
                    },
                    date,
                    lastN = 'millisecond'; // for sub-millisecond data, #4223

                if (closestPointRange) {
                    date = dateFormat('%m-%d %H:%M:%S.%L', point.x);
                    for (n in timeUnits) {

                        // If the range is exactly one week and we're looking at a Sunday/Monday, go for the week format
                        if (closestPointRange === timeUnits.week && +dateFormat('%w', point.x) === xAxis.options.startOfWeek &&
                            date.substr(6) === blank.substr(6)) {
                            n = 'week';
                            break;
                        }

                        // The first format that is too great for the range
                        if (timeUnits[n] > closestPointRange) {
                            n = lastN;
                            break;
                        }

                        // If the point is placed every day at 23:59, we need to show
                        // the minutes as well. #2637.
                        if (strpos[n] && date.substr(strpos[n]) !== blank.substr(strpos[n])) {
                            break;
                        }

                        // Weeks are outside the hierarchy, only apply them on Mondays/Sundays like in the first condition
                        if (n !== 'week') {
                            lastN = n;
                        }
                    }

                    if (n) {
                        xDateFormat = dateTimeLabelFormats[n];
                    }
                } else {
                    xDateFormat = dateTimeLabelFormats.day;
                }

                return xDateFormat || dateTimeLabelFormats.year; // #2546, 2581
            },

            /**
             * Format the footer/header of the tooltip
             * #3397: abstraction to enable formatting of footer and header
             */
            tooltipFooterHeaderFormatter: function(labelConfig, isFooter) {
                var footOrHead = isFooter ? 'footer' : 'header',
                    series = labelConfig.series,
                    tooltipOptions = series.tooltipOptions,
                    xDateFormat = tooltipOptions.xDateFormat,
                    xAxis = series.xAxis,
                    isDateTime = xAxis && xAxis.options.type === 'datetime' && isNumber(labelConfig.key),
                    formatString = tooltipOptions[footOrHead + 'Format'];

                // Guess the best date format based on the closest point distance (#568, #3418)
                if (isDateTime && !xDateFormat) {
                    xDateFormat = this.getXDateFormat(labelConfig, tooltipOptions, xAxis);
                }

                // Insert the footer date format if any
                if (isDateTime && xDateFormat) {
                    formatString = formatString.replace('{point.key}', '{point.key:' + xDateFormat + '}');
                }

                return format(formatString, {
                    point: labelConfig,
                    series: series
                });
            },

            /**
             * Build the body (lines) of the tooltip by iterating over the items and returning one entry for each item,
             * abstracting this functionality allows to easily overwrite and extend it.
             */
            bodyFormatter: function(items) {
                return map(items, function(item) {
                    var tooltipOptions = item.series.tooltipOptions;
                    return (tooltipOptions.pointFormatter || item.point.tooltipFormatter)
                        .call(item.point, tooltipOptions.pointFormat);
                });
            }

        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var addEvent = H.addEvent,
            attr = H.attr,
            charts = H.charts,
            color = H.color,
            css = H.css,
            defined = H.defined,
            doc = H.doc,
            each = H.each,
            extend = H.extend,
            fireEvent = H.fireEvent,
            offset = H.offset,
            pick = H.pick,
            removeEvent = H.removeEvent,
            splat = H.splat,
            Tooltip = H.Tooltip,
            win = H.win;

        /**
         * The mouse tracker object. All methods starting with "on" are primary DOM
         * event handlers. Subsequent methods should be named differently from what they
         * are doing.
         *
         * @constructor Pointer
         * @param {Object} chart The Chart instance
         * @param {Object} options The root options object
         */
        H.Pointer = function(chart, options) {
            this.init(chart, options);
        };

        H.Pointer.prototype = {
            /**
             * Initialize Pointer
             */
            init: function(chart, options) {

                // Store references
                this.options = options;
                this.chart = chart;

                // Do we need to handle click on a touch device?
                this.runChartClick = options.chart.events && !!options.chart.events.click;

                this.pinchDown = [];
                this.lastValidTouch = {};

                if (Tooltip && options.tooltip.enabled) {
                    chart.tooltip = new Tooltip(chart, options.tooltip);
                    this.followTouchMove = pick(options.tooltip.followTouchMove, true);
                }

                this.setDOMEvents();
            },

            /**
             * Resolve the zoomType option, this is reset on all touch start and mouse
             * down events.
             */
            zoomOption: function(e) {
                var chart = this.chart,
                    options = chart.options.chart,
                    zoomType = options.zoomType || '',
                    inverted = chart.inverted,
                    zoomX,
                    zoomY;

                // Look for the pinchType option
                if (/touch/.test(e.type)) {
                    zoomType = pick(options.pinchType, zoomType);
                }

                this.zoomX = zoomX = /x/.test(zoomType);
                this.zoomY = zoomY = /y/.test(zoomType);
                this.zoomHor = (zoomX && !inverted) || (zoomY && inverted);
                this.zoomVert = (zoomY && !inverted) || (zoomX && inverted);
                this.hasZoom = zoomX || zoomY;
            },

            /**
             * Add crossbrowser support for chartX and chartY
             * @param {Object} e The event object in standard browsers
             */
            normalize: function(e, chartPosition) {
                var chartX,
                    chartY,
                    ePos;

                // IE normalizing
                e = e || win.event;
                if (!e.target) {
                    e.target = e.srcElement;
                }

                // iOS (#2757)
                ePos = e.touches ? (e.touches.length ? e.touches.item(0) : e.changedTouches[0]) : e;

                // Get mouse position
                if (!chartPosition) {
                    this.chartPosition = chartPosition = offset(this.chart.container);
                }

                // chartX and chartY
                if (ePos.pageX === undefined) { // IE < 9. #886.
                    chartX = Math.max(e.x, e.clientX - chartPosition.left); // #2005, #2129: the second case is 
                    // for IE10 quirks mode within framesets
                    chartY = e.y;
                } else {
                    chartX = ePos.pageX - chartPosition.left;
                    chartY = ePos.pageY - chartPosition.top;
                }

                return extend(e, {
                    chartX: Math.round(chartX),
                    chartY: Math.round(chartY)
                });
            },

            /**
             * Get the click position in terms of axis values.
             *
             * @param {Object} e A pointer event
             */
            getCoordinates: function(e) {
                var coordinates = {
                    xAxis: [],
                    yAxis: []
                };

                each(this.chart.axes, function(axis) {
                    coordinates[axis.isXAxis ? 'xAxis' : 'yAxis'].push({
                        axis: axis,
                        value: axis.toValue(e[axis.horiz ? 'chartX' : 'chartY'])
                    });
                });
                return coordinates;
            },

            /**
             * With line type charts with a single tracker, get the point closest to the mouse.
             * Run Point.onMouseOver and display tooltip for the point or points.
             */
            runPointActions: function(e) {

                var pointer = this,
                    chart = pointer.chart,
                    series = chart.series,
                    tooltip = chart.tooltip,
                    shared = tooltip ? tooltip.shared : false,
                    followPointer,
                    updatePosition = true,
                    hoverPoint = chart.hoverPoint,
                    hoverSeries = chart.hoverSeries,
                    i,
                    anchor,
                    noSharedTooltip,
                    stickToHoverSeries,
                    directTouch,
                    kdpoints = [],
                    kdpointT;

                // For hovering over the empty parts of the plot area (hoverSeries is undefined).
                // If there is one series with point tracking (combo chart), don't go to nearest neighbour.
                if (!shared && !hoverSeries) {
                    for (i = 0; i < series.length; i++) {
                        if (series[i].directTouch || !series[i].options.stickyTracking) {
                            series = [];
                        }
                    }
                }

                // If it has a hoverPoint and that series requires direct touch (like columns, #3899), or we're on
                // a noSharedTooltip series among shared tooltip series (#4546), use the hoverPoint . Otherwise,
                // search the k-d tree.
                stickToHoverSeries = hoverSeries && (shared ? hoverSeries.noSharedTooltip : hoverSeries.directTouch);
                if (stickToHoverSeries && hoverPoint) {
                    kdpoints = [hoverPoint];

                    // Handle shared tooltip or cases where a series is not yet hovered
                } else {
                    // When we have non-shared tooltip and sticky tracking is disabled,
                    // search for the closest point only on hovered series: #5533, #5476
                    if (!shared && hoverSeries && !hoverSeries.options.stickyTracking) {
                        series = [hoverSeries];
                    }
                    // Find nearest points on all series
                    each(series, function(s) {
                        // Skip hidden series
                        noSharedTooltip = s.noSharedTooltip && shared;
                        directTouch = !shared && s.directTouch;
                        if (s.visible && !noSharedTooltip && !directTouch && pick(s.options.enableMouseTracking, true)) { // #3821
                            kdpointT = s.searchPoint(e, !noSharedTooltip && s.kdDimensions === 1); // #3828
                            if (kdpointT && kdpointT.series) { // Point.series becomes null when reset and before redraw (#5197)
                                kdpoints.push(kdpointT);
                            }
                        }
                    });

                    // Sort kdpoints by distance to mouse pointer
                    kdpoints.sort(function(p1, p2) {
                        var isCloserX = p1.distX - p2.distX,
                            isCloser = p1.dist - p2.dist,
                            isAbove = p2.series.group.zIndex - p1.series.group.zIndex;

                        // We have two points which are not in the same place on xAxis and shared tooltip:
                        if (isCloserX !== 0 && shared) { // #5721
                            return isCloserX;
                        }
                        // Points are not exactly in the same place on x/yAxis:
                        if (isCloser !== 0) {
                            return isCloser;
                        }
                        // The same xAxis and yAxis position, sort by z-index:
                        if (isAbove !== 0) {
                            return isAbove;
                        }

                        // The same zIndex, sort by array index:
                        return p1.series.index > p2.series.index ? -1 : 1;
                    });
                }

                // Remove points with different x-positions, required for shared tooltip and crosshairs (#4645):
                if (shared) {
                    i = kdpoints.length;
                    while (i--) {
                        if (kdpoints[i].x !== kdpoints[0].x || kdpoints[i].series.noSharedTooltip) {
                            kdpoints.splice(i, 1);
                        }
                    }
                }

                // Refresh tooltip for kdpoint if new hover point or tooltip was hidden // #3926, #4200
                if (kdpoints[0] && (kdpoints[0] !== this.prevKDPoint || (tooltip && tooltip.isHidden))) {
                    // Draw tooltip if necessary
                    if (shared && !kdpoints[0].series.noSharedTooltip) {
                        // Do mouseover on all points (#3919, #3985, #4410, #5622)
                        for (i = 0; i < kdpoints.length; i++) {
                            kdpoints[i].onMouseOver(e, kdpoints[i] !== ((hoverSeries && hoverSeries.directTouch && hoverPoint) || kdpoints[0]));
                        }

                        if (kdpoints.length && tooltip) {
                            // Keep the order of series in tooltip:
                            tooltip.refresh(kdpoints.sort(function(p1, p2) {
                                return p1.series.index - p2.series.index;
                            }), e);
                        }
                    } else {
                        if (tooltip) {
                            tooltip.refresh(kdpoints[0], e);
                        }
                        if (!hoverSeries || !hoverSeries.directTouch) { // #4448
                            kdpoints[0].onMouseOver(e);
                        }
                    }
                    this.prevKDPoint = kdpoints[0];
                    updatePosition = false;
                }
                // Update positions (regardless of kdpoint or hoverPoint)
                if (updatePosition) {
                    followPointer = hoverSeries && hoverSeries.tooltipOptions.followPointer;
                    if (tooltip && followPointer && !tooltip.isHidden) {
                        anchor = tooltip.getAnchor([{}], e);
                        tooltip.updatePosition({
                            plotX: anchor[0],
                            plotY: anchor[1]
                        });
                    }
                }

                // Start the event listener to pick up the tooltip and crosshairs
                if (!pointer.unDocMouseMove) {
                    pointer.unDocMouseMove = addEvent(doc, 'mousemove', function(e) {
                        if (charts[H.hoverChartIndex]) {
                            charts[H.hoverChartIndex].pointer.onDocumentMouseMove(e);
                        }
                    });
                }

                // Crosshair. For each hover point, loop over axes and draw cross if that point
                // belongs to the axis (#4927).
                each(shared ? kdpoints : [pick(hoverPoint, kdpoints[0])], function drawPointCrosshair(point) { // #5269
                    each(chart.axes, function drawAxisCrosshair(axis) {
                        // In case of snap = false, point is undefined, and we draw the crosshair anyway (#5066)
                        if (!point || point.series && point.series[axis.coll] === axis) { // #5658
                            axis.drawCrosshair(e, point);
                        }
                    });
                });
            },

            /**
             * Reset the tracking by hiding the tooltip, the hover series state and the hover point
             *
             * @param allowMove {Boolean} Instead of destroying the tooltip altogether, allow moving it if possible
             */
            reset: function(allowMove, delay) {
                var pointer = this,
                    chart = pointer.chart,
                    hoverSeries = chart.hoverSeries,
                    hoverPoint = chart.hoverPoint,
                    hoverPoints = chart.hoverPoints,
                    tooltip = chart.tooltip,
                    tooltipPoints = tooltip && tooltip.shared ? hoverPoints : hoverPoint;

                // Check if the points have moved outside the plot area (#1003, #4736, #5101)
                if (allowMove && tooltipPoints) {
                    each(splat(tooltipPoints), function(point) {
                        if (point.series.isCartesian && point.plotX === undefined) {
                            allowMove = false;
                        }
                    });
                }

                // Just move the tooltip, #349
                if (allowMove) {
                    if (tooltip && tooltipPoints) {
                        tooltip.refresh(tooltipPoints);
                        if (hoverPoint) { // #2500
                            hoverPoint.setState(hoverPoint.state, true);
                            each(chart.axes, function(axis) {
                                if (axis.crosshair) {
                                    axis.drawCrosshair(null, hoverPoint);
                                }
                            });
                        }
                    }

                    // Full reset
                } else {

                    if (hoverPoint) {
                        hoverPoint.onMouseOut();
                    }

                    if (hoverPoints) {
                        each(hoverPoints, function(point) {
                            point.setState();
                        });
                    }

                    if (hoverSeries) {
                        hoverSeries.onMouseOut();
                    }

                    if (tooltip) {
                        tooltip.hide(delay);
                    }

                    if (pointer.unDocMouseMove) {
                        pointer.unDocMouseMove = pointer.unDocMouseMove();
                    }

                    // Remove crosshairs
                    each(chart.axes, function(axis) {
                        axis.hideCrosshair();
                    });

                    pointer.hoverX = pointer.prevKDPoint = chart.hoverPoints = chart.hoverPoint = null;
                }
            },

            /**
             * Scale series groups to a certain scale and translation
             */
            scaleGroups: function(attribs, clip) {

                var chart = this.chart,
                    seriesAttribs;

                // Scale each series
                each(chart.series, function(series) {
                    seriesAttribs = attribs || series.getPlotBox(); // #1701
                    if (series.xAxis && series.xAxis.zoomEnabled && series.group) {
                        series.group.attr(seriesAttribs);
                        if (series.markerGroup) {
                            series.markerGroup.attr(seriesAttribs);
                            series.markerGroup.clip(clip ? chart.clipRect : null);
                        }
                        if (series.dataLabelsGroup) {
                            series.dataLabelsGroup.attr(seriesAttribs);
                        }
                    }
                });

                // Clip
                chart.clipRect.attr(clip || chart.clipBox);
            },

            /**
             * Start a drag operation
             */
            dragStart: function(e) {
                var chart = this.chart;

                // Record the start position
                chart.mouseIsDown = e.type;
                chart.cancelClick = false;
                chart.mouseDownX = this.mouseDownX = e.chartX;
                chart.mouseDownY = this.mouseDownY = e.chartY;
            },

            /**
             * Perform a drag operation in response to a mousemove event while the mouse is down
             */
            drag: function(e) {

                var chart = this.chart,
                    chartOptions = chart.options.chart,
                    chartX = e.chartX,
                    chartY = e.chartY,
                    zoomHor = this.zoomHor,
                    zoomVert = this.zoomVert,
                    plotLeft = chart.plotLeft,
                    plotTop = chart.plotTop,
                    plotWidth = chart.plotWidth,
                    plotHeight = chart.plotHeight,
                    clickedInside,
                    size,
                    selectionMarker = this.selectionMarker,
                    mouseDownX = this.mouseDownX,
                    mouseDownY = this.mouseDownY,
                    panKey = chartOptions.panKey && e[chartOptions.panKey + 'Key'];

                // If the device supports both touch and mouse (like IE11), and we are touch-dragging
                // inside the plot area, don't handle the mouse event. #4339.
                if (selectionMarker && selectionMarker.touch) {
                    return;
                }

                // If the mouse is outside the plot area, adjust to cooordinates
                // inside to prevent the selection marker from going outside
                if (chartX < plotLeft) {
                    chartX = plotLeft;
                } else if (chartX > plotLeft + plotWidth) {
                    chartX = plotLeft + plotWidth;
                }

                if (chartY < plotTop) {
                    chartY = plotTop;
                } else if (chartY > plotTop + plotHeight) {
                    chartY = plotTop + plotHeight;
                }

                // determine if the mouse has moved more than 10px
                this.hasDragged = Math.sqrt(
                    Math.pow(mouseDownX - chartX, 2) +
                    Math.pow(mouseDownY - chartY, 2)
                );

                if (this.hasDragged > 10) {
                    clickedInside = chart.isInsidePlot(mouseDownX - plotLeft, mouseDownY - plotTop);

                    // make a selection
                    if (chart.hasCartesianSeries && (this.zoomX || this.zoomY) && clickedInside && !panKey) {
                        if (!selectionMarker) {
                            this.selectionMarker = selectionMarker = chart.renderer.rect(
                                    plotLeft,
                                    plotTop,
                                    zoomHor ? 1 : plotWidth,
                                    zoomVert ? 1 : plotHeight,
                                    0
                                )
                                .attr({

                                    fill: chartOptions.selectionMarkerFill || color('#335cad').setOpacity(0.25).get(),

                                    'class': 'highcharts-selection-marker',
                                    'zIndex': 7
                                })
                                .add();
                        }
                    }

                    // adjust the width of the selection marker
                    if (selectionMarker && zoomHor) {
                        size = chartX - mouseDownX;
                        selectionMarker.attr({
                            width: Math.abs(size),
                            x: (size > 0 ? 0 : size) + mouseDownX
                        });
                    }
                    // adjust the height of the selection marker
                    if (selectionMarker && zoomVert) {
                        size = chartY - mouseDownY;
                        selectionMarker.attr({
                            height: Math.abs(size),
                            y: (size > 0 ? 0 : size) + mouseDownY
                        });
                    }

                    // panning
                    if (clickedInside && !selectionMarker && chartOptions.panning) {
                        chart.pan(e, chartOptions.panning);
                    }
                }
            },

            /**
             * On mouse up or touch end across the entire document, drop the selection.
             */
            drop: function(e) {
                var pointer = this,
                    chart = this.chart,
                    hasPinched = this.hasPinched;

                if (this.selectionMarker) {
                    var selectionData = {
                            originalEvent: e, // #4890
                            xAxis: [],
                            yAxis: []
                        },
                        selectionBox = this.selectionMarker,
                        selectionLeft = selectionBox.attr ? selectionBox.attr('x') : selectionBox.x,
                        selectionTop = selectionBox.attr ? selectionBox.attr('y') : selectionBox.y,
                        selectionWidth = selectionBox.attr ? selectionBox.attr('width') : selectionBox.width,
                        selectionHeight = selectionBox.attr ? selectionBox.attr('height') : selectionBox.height,
                        runZoom;

                    // a selection has been made
                    if (this.hasDragged || hasPinched) {

                        // record each axis' min and max
                        each(chart.axes, function(axis) {
                            if (axis.zoomEnabled && defined(axis.min) && (hasPinched || pointer[{
                                    xAxis: 'zoomX',
                                    yAxis: 'zoomY'
                                }[axis.coll]])) { // #859, #3569
                                var horiz = axis.horiz,
                                    minPixelPadding = e.type === 'touchend' ? axis.minPixelPadding : 0, // #1207, #3075
                                    selectionMin = axis.toValue((horiz ? selectionLeft : selectionTop) + minPixelPadding),
                                    selectionMax = axis.toValue((horiz ? selectionLeft + selectionWidth : selectionTop + selectionHeight) - minPixelPadding);

                                selectionData[axis.coll].push({
                                    axis: axis,
                                    min: Math.min(selectionMin, selectionMax), // for reversed axes
                                    max: Math.max(selectionMin, selectionMax)
                                });
                                runZoom = true;
                            }
                        });
                        if (runZoom) {
                            fireEvent(chart, 'selection', selectionData, function(args) {
                                chart.zoom(extend(args, hasPinched ? {
                                    animation: false
                                } : null));
                            });
                        }

                    }
                    this.selectionMarker = this.selectionMarker.destroy();

                    // Reset scaling preview
                    if (hasPinched) {
                        this.scaleGroups();
                    }
                }

                // Reset all
                if (chart) { // it may be destroyed on mouse up - #877
                    css(chart.container, {
                        cursor: chart._cursor
                    });
                    chart.cancelClick = this.hasDragged > 10; // #370
                    chart.mouseIsDown = this.hasDragged = this.hasPinched = false;
                    this.pinchDown = [];
                }
            },

            onContainerMouseDown: function(e) {

                e = this.normalize(e);

                this.zoomOption(e);

                // issue #295, dragging not always working in Firefox
                if (e.preventDefault) {
                    e.preventDefault();
                }

                this.dragStart(e);
            },



            onDocumentMouseUp: function(e) {
                if (charts[H.hoverChartIndex]) {
                    charts[H.hoverChartIndex].pointer.drop(e);
                }
            },

            /**
             * Special handler for mouse move that will hide the tooltip when the mouse leaves the plotarea.
             * Issue #149 workaround. The mouseleave event does not always fire.
             */
            onDocumentMouseMove: function(e) {
                var chart = this.chart,
                    chartPosition = this.chartPosition;

                e = this.normalize(e, chartPosition);

                // If we're outside, hide the tooltip
                if (chartPosition && !this.inClass(e.target, 'highcharts-tracker') &&
                    !chart.isInsidePlot(e.chartX - chart.plotLeft, e.chartY - chart.plotTop)) {
                    this.reset();
                }
            },

            /**
             * When mouse leaves the container, hide the tooltip.
             */
            onContainerMouseLeave: function(e) {
                var chart = charts[H.hoverChartIndex];
                if (chart && (e.relatedTarget || e.toElement)) { // #4886, MS Touch end fires mouseleave but with no related target
                    chart.pointer.reset();
                    chart.pointer.chartPosition = null; // also reset the chart position, used in #149 fix
                }
            },

            // The mousemove, touchmove and touchstart event handler
            onContainerMouseMove: function(e) {

                var chart = this.chart;

                if (!defined(H.hoverChartIndex) || !charts[H.hoverChartIndex] || !charts[H.hoverChartIndex].mouseIsDown) {
                    H.hoverChartIndex = chart.index;
                }

                e = this.normalize(e);
                e.returnValue = false; // #2251, #3224

                if (chart.mouseIsDown === 'mousedown') {
                    this.drag(e);
                }

                // Show the tooltip and run mouse over events (#977)
                if ((this.inClass(e.target, 'highcharts-tracker') ||
                        chart.isInsidePlot(e.chartX - chart.plotLeft, e.chartY - chart.plotTop)) && !chart.openMenu) {
                    this.runPointActions(e);
                }
            },

            /**
             * Utility to detect whether an element has, or has a parent with, a specific
             * class name. Used on detection of tracker objects and on deciding whether
             * hovering the tooltip should cause the active series to mouse out.
             */
            inClass: function(element, className) {
                var elemClassName;
                while (element) {
                    elemClassName = attr(element, 'class');
                    if (elemClassName) {
                        if (elemClassName.indexOf(className) !== -1) {
                            return true;
                        }
                        if (elemClassName.indexOf('highcharts-container') !== -1) {
                            return false;
                        }
                    }
                    element = element.parentNode;
                }
            },

            onTrackerMouseOut: function(e) {
                var series = this.chart.hoverSeries,
                    relatedTarget = e.relatedTarget || e.toElement;

                if (series && relatedTarget && !series.options.stickyTracking &&
                    !this.inClass(relatedTarget, 'highcharts-tooltip') &&
                    (!this.inClass(relatedTarget, 'highcharts-series-' + series.index) || // #2499, #4465
                        !this.inClass(relatedTarget, 'highcharts-tracker') // #5553
                    )
                ) {
                    series.onMouseOut();
                }
            },

            onContainerClick: function(e) {
                var chart = this.chart,
                    hoverPoint = chart.hoverPoint,
                    plotLeft = chart.plotLeft,
                    plotTop = chart.plotTop;

                e = this.normalize(e);

                if (!chart.cancelClick) {

                    // On tracker click, fire the series and point events. #783, #1583
                    if (hoverPoint && this.inClass(e.target, 'highcharts-tracker')) {

                        // the series click event
                        fireEvent(hoverPoint.series, 'click', extend(e, {
                            point: hoverPoint
                        }));

                        // the point click event
                        if (chart.hoverPoint) { // it may be destroyed (#1844)
                            hoverPoint.firePointEvent('click', e);
                        }

                        // When clicking outside a tracker, fire a chart event
                    } else {
                        extend(e, this.getCoordinates(e));

                        // fire a click event in the chart
                        if (chart.isInsidePlot(e.chartX - plotLeft, e.chartY - plotTop)) {
                            fireEvent(chart, 'click', e);
                        }
                    }


                }
            },

            /**
             * Set the JS DOM events on the container and document. This method should contain
             * a one-to-one assignment between methods and their handlers. Any advanced logic should
             * be moved to the handler reflecting the event's name.
             */
            setDOMEvents: function() {

                var pointer = this,
                    container = pointer.chart.container;

                container.onmousedown = function(e) {
                    pointer.onContainerMouseDown(e);
                };
                container.onmousemove = function(e) {
                    pointer.onContainerMouseMove(e);
                };
                container.onclick = function(e) {
                    pointer.onContainerClick(e);
                };
                addEvent(container, 'mouseleave', pointer.onContainerMouseLeave);
                if (H.chartCount === 1) {
                    addEvent(doc, 'mouseup', pointer.onDocumentMouseUp);
                }
                if (H.hasTouch) {
                    container.ontouchstart = function(e) {
                        pointer.onContainerTouchStart(e);
                    };
                    container.ontouchmove = function(e) {
                        pointer.onContainerTouchMove(e);
                    };
                    if (H.chartCount === 1) {
                        addEvent(doc, 'touchend', pointer.onDocumentTouchEnd);
                    }
                }

            },

            /**
             * Destroys the Pointer object and disconnects DOM events.
             */
            destroy: function() {
                var prop;

                removeEvent(this.chart.container, 'mouseleave', this.onContainerMouseLeave);
                if (!H.chartCount) {
                    removeEvent(doc, 'mouseup', this.onDocumentMouseUp);
                    removeEvent(doc, 'touchend', this.onDocumentTouchEnd);
                }

                // memory and CPU leak
                clearInterval(this.tooltipTimeout);

                for (prop in this) {
                    this[prop] = null;
                }
            }
        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var charts = H.charts,
            each = H.each,
            extend = H.extend,
            map = H.map,
            noop = H.noop,
            pick = H.pick,
            Pointer = H.Pointer;

        /* Support for touch devices */
        extend(Pointer.prototype, /** @lends Pointer.prototype */ {

            /**
             * Run translation operations
             */
            pinchTranslate: function(pinchDown, touches, transform, selectionMarker, clip, lastValidTouch) {
                if (this.zoomHor) {
                    this.pinchTranslateDirection(true, pinchDown, touches, transform, selectionMarker, clip, lastValidTouch);
                }
                if (this.zoomVert) {
                    this.pinchTranslateDirection(false, pinchDown, touches, transform, selectionMarker, clip, lastValidTouch);
                }
            },

            /**
             * Run translation operations for each direction (horizontal and vertical) independently
             */
            pinchTranslateDirection: function(horiz, pinchDown, touches, transform,
                selectionMarker, clip, lastValidTouch, forcedScale) {
                var chart = this.chart,
                    xy = horiz ? 'x' : 'y',
                    XY = horiz ? 'X' : 'Y',
                    sChartXY = 'chart' + XY,
                    wh = horiz ? 'width' : 'height',
                    plotLeftTop = chart['plot' + (horiz ? 'Left' : 'Top')],
                    selectionWH,
                    selectionXY,
                    clipXY,
                    scale = forcedScale || 1,
                    inverted = chart.inverted,
                    bounds = chart.bounds[horiz ? 'h' : 'v'],
                    singleTouch = pinchDown.length === 1,
                    touch0Start = pinchDown[0][sChartXY],
                    touch0Now = touches[0][sChartXY],
                    touch1Start = !singleTouch && pinchDown[1][sChartXY],
                    touch1Now = !singleTouch && touches[1][sChartXY],
                    outOfBounds,
                    transformScale,
                    scaleKey,
                    setScale = function() {
                        // Don't zoom if fingers are too close on this axis
                        if (!singleTouch && Math.abs(touch0Start - touch1Start) > 20) {
                            scale = forcedScale || Math.abs(touch0Now - touch1Now) / Math.abs(touch0Start - touch1Start);
                        }

                        clipXY = ((plotLeftTop - touch0Now) / scale) + touch0Start;
                        selectionWH = chart['plot' + (horiz ? 'Width' : 'Height')] / scale;
                    };

                // Set the scale, first pass
                setScale();

                selectionXY = clipXY; // the clip position (x or y) is altered if out of bounds, the selection position is not

                // Out of bounds
                if (selectionXY < bounds.min) {
                    selectionXY = bounds.min;
                    outOfBounds = true;
                } else if (selectionXY + selectionWH > bounds.max) {
                    selectionXY = bounds.max - selectionWH;
                    outOfBounds = true;
                }

                // Is the chart dragged off its bounds, determined by dataMin and dataMax?
                if (outOfBounds) {

                    // Modify the touchNow position in order to create an elastic drag movement. This indicates
                    // to the user that the chart is responsive but can't be dragged further.
                    touch0Now -= 0.8 * (touch0Now - lastValidTouch[xy][0]);
                    if (!singleTouch) {
                        touch1Now -= 0.8 * (touch1Now - lastValidTouch[xy][1]);
                    }

                    // Set the scale, second pass to adapt to the modified touchNow positions
                    setScale();

                } else {
                    lastValidTouch[xy] = [touch0Now, touch1Now];
                }

                // Set geometry for clipping, selection and transformation
                if (!inverted) {
                    clip[xy] = clipXY - plotLeftTop;
                    clip[wh] = selectionWH;
                }
                scaleKey = inverted ? (horiz ? 'scaleY' : 'scaleX') : 'scale' + XY;
                transformScale = inverted ? 1 / scale : scale;

                selectionMarker[wh] = selectionWH;
                selectionMarker[xy] = selectionXY;
                transform[scaleKey] = scale;
                transform['translate' + XY] = (transformScale * plotLeftTop) + (touch0Now - (transformScale * touch0Start));
            },

            /**
             * Handle touch events with two touches
             */
            pinch: function(e) {

                var self = this,
                    chart = self.chart,
                    pinchDown = self.pinchDown,
                    touches = e.touches,
                    touchesLength = touches.length,
                    lastValidTouch = self.lastValidTouch,
                    hasZoom = self.hasZoom,
                    selectionMarker = self.selectionMarker,
                    transform = {},
                    fireClickEvent = touchesLength === 1 && ((self.inClass(e.target, 'highcharts-tracker') &&
                        chart.runTrackerClick) || self.runChartClick),
                    clip = {};

                // Don't initiate panning until the user has pinched. This prevents us from
                // blocking page scrolling as users scroll down a long page (#4210).
                if (touchesLength > 1) {
                    self.initiated = true;
                }

                // On touch devices, only proceed to trigger click if a handler is defined
                if (hasZoom && self.initiated && !fireClickEvent) {
                    e.preventDefault();
                }

                // Normalize each touch
                map(touches, function(e) {
                    return self.normalize(e);
                });

                // Register the touch start position
                if (e.type === 'touchstart') {
                    each(touches, function(e, i) {
                        pinchDown[i] = {
                            chartX: e.chartX,
                            chartY: e.chartY
                        };
                    });
                    lastValidTouch.x = [pinchDown[0].chartX, pinchDown[1] && pinchDown[1].chartX];
                    lastValidTouch.y = [pinchDown[0].chartY, pinchDown[1] && pinchDown[1].chartY];

                    // Identify the data bounds in pixels
                    each(chart.axes, function(axis) {
                        if (axis.zoomEnabled) {
                            var bounds = chart.bounds[axis.horiz ? 'h' : 'v'],
                                minPixelPadding = axis.minPixelPadding,
                                min = axis.toPixels(pick(axis.options.min, axis.dataMin)),
                                max = axis.toPixels(pick(axis.options.max, axis.dataMax)),
                                absMin = Math.min(min, max),
                                absMax = Math.max(min, max);

                            // Store the bounds for use in the touchmove handler
                            bounds.min = Math.min(axis.pos, absMin - minPixelPadding);
                            bounds.max = Math.max(axis.pos + axis.len, absMax + minPixelPadding);
                        }
                    });
                    self.res = true; // reset on next move

                    // Optionally move the tooltip on touchmove
                } else if (self.followTouchMove && touchesLength === 1) {
                    this.runPointActions(self.normalize(e));

                    // Event type is touchmove, handle panning and pinching
                } else if (pinchDown.length) { // can be 0 when releasing, if touchend fires first


                    // Set the marker
                    if (!selectionMarker) {
                        self.selectionMarker = selectionMarker = extend({
                            destroy: noop,
                            touch: true
                        }, chart.plotBox);
                    }

                    self.pinchTranslate(pinchDown, touches, transform, selectionMarker, clip, lastValidTouch);

                    self.hasPinched = hasZoom;

                    // Scale and translate the groups to provide visual feedback during pinching
                    self.scaleGroups(transform, clip);

                    if (self.res) {
                        self.res = false;
                        this.reset(false, 0);
                    }
                }
            },

            /**
             * General touch handler shared by touchstart and touchmove.
             */
            touch: function(e, start) {
                var chart = this.chart,
                    hasMoved,
                    pinchDown,
                    isInside;

                if (chart.index !== H.hoverChartIndex) {
                    this.onContainerMouseLeave({
                        relatedTarget: true
                    });
                }
                H.hoverChartIndex = chart.index;

                if (e.touches.length === 1) {

                    e = this.normalize(e);

                    isInside = chart.isInsidePlot(
                        e.chartX - chart.plotLeft,
                        e.chartY - chart.plotTop
                    );
                    if (isInside && !chart.openMenu) {

                        // Run mouse events and display tooltip etc
                        if (start) {
                            this.runPointActions(e);
                        }

                        // Android fires touchmove events after the touchstart even if the
                        // finger hasn't moved, or moved only a pixel or two. In iOS however,
                        // the touchmove doesn't fire unless the finger moves more than ~4px.
                        // So we emulate this behaviour in Android by checking how much it
                        // moved, and cancelling on small distances. #3450.
                        if (e.type === 'touchmove') {
                            pinchDown = this.pinchDown;
                            hasMoved = pinchDown[0] ? Math.sqrt( // #5266
                                Math.pow(pinchDown[0].chartX - e.chartX, 2) +
                                Math.pow(pinchDown[0].chartY - e.chartY, 2)
                            ) >= 4 : false;
                        }

                        if (pick(hasMoved, true)) {
                            this.pinch(e);
                        }

                    } else if (start) {
                        // Hide the tooltip on touching outside the plot area (#1203)
                        this.reset();
                    }

                } else if (e.touches.length === 2) {
                    this.pinch(e);
                }
            },

            onContainerTouchStart: function(e) {
                this.zoomOption(e);
                this.touch(e, true);
            },

            onContainerTouchMove: function(e) {
                this.touch(e);
            },

            onDocumentTouchEnd: function(e) {
                if (charts[H.hoverChartIndex]) {
                    charts[H.hoverChartIndex].pointer.drop(e);
                }
            }

        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var addEvent = H.addEvent,
            charts = H.charts,
            css = H.css,
            doc = H.doc,
            extend = H.extend,
            noop = H.noop,
            Pointer = H.Pointer,
            removeEvent = H.removeEvent,
            win = H.win,
            wrap = H.wrap;

        if (win.PointerEvent || win.MSPointerEvent) {

            // The touches object keeps track of the points being touched at all times
            var touches = {},
                hasPointerEvent = !!win.PointerEvent,
                getWebkitTouches = function() {
                    var key,
                        fake = [];
                    fake.item = function(i) {
                        return this[i];
                    };
                    for (key in touches) {
                        if (touches.hasOwnProperty(key)) {
                            fake.push({
                                pageX: touches[key].pageX,
                                pageY: touches[key].pageY,
                                target: touches[key].target
                            });
                        }
                    }
                    return fake;
                },
                translateMSPointer = function(e, method, wktype, func) {
                    var p;
                    if ((e.pointerType === 'touch' || e.pointerType === e.MSPOINTER_TYPE_TOUCH) && charts[H.hoverChartIndex]) {
                        func(e);
                        p = charts[H.hoverChartIndex].pointer;
                        p[method]({
                            type: wktype,
                            target: e.currentTarget,
                            preventDefault: noop,
                            touches: getWebkitTouches()
                        });
                    }
                };

            /**
             * Extend the Pointer prototype with methods for each event handler and more
             */
            extend(Pointer.prototype, /** @lends Pointer.prototype */ {
                onContainerPointerDown: function(e) {
                    translateMSPointer(e, 'onContainerTouchStart', 'touchstart', function(e) {
                        touches[e.pointerId] = {
                            pageX: e.pageX,
                            pageY: e.pageY,
                            target: e.currentTarget
                        };
                    });
                },
                onContainerPointerMove: function(e) {
                    translateMSPointer(e, 'onContainerTouchMove', 'touchmove', function(e) {
                        touches[e.pointerId] = {
                            pageX: e.pageX,
                            pageY: e.pageY
                        };
                        if (!touches[e.pointerId].target) {
                            touches[e.pointerId].target = e.currentTarget;
                        }
                    });
                },
                onDocumentPointerUp: function(e) {
                    translateMSPointer(e, 'onDocumentTouchEnd', 'touchend', function(e) {
                        delete touches[e.pointerId];
                    });
                },

                /**
                 * Add or remove the MS Pointer specific events
                 */
                batchMSEvents: function(fn) {
                    fn(this.chart.container, hasPointerEvent ? 'pointerdown' : 'MSPointerDown', this.onContainerPointerDown);
                    fn(this.chart.container, hasPointerEvent ? 'pointermove' : 'MSPointerMove', this.onContainerPointerMove);
                    fn(doc, hasPointerEvent ? 'pointerup' : 'MSPointerUp', this.onDocumentPointerUp);
                }
            });

            // Disable default IE actions for pinch and such on chart element
            wrap(Pointer.prototype, 'init', function(proceed, chart, options) {
                proceed.call(this, chart, options);
                if (this.hasZoom) { // #4014
                    css(chart.container, {
                        '-ms-touch-action': 'none',
                        'touch-action': 'none'
                    });
                }
            });

            // Add IE specific touch events to chart
            wrap(Pointer.prototype, 'setDOMEvents', function(proceed) {
                proceed.apply(this);
                if (this.hasZoom || this.followTouchMove) {
                    this.batchMSEvents(addEvent);
                }
            });
            // Destroy MS events also
            wrap(Pointer.prototype, 'destroy', function(proceed) {
                this.batchMSEvents(removeEvent);
                proceed.call(this);
            });
        }

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var Legend,

            addEvent = H.addEvent,
            css = H.css,
            discardElement = H.discardElement,
            defined = H.defined,
            each = H.each,
            extend = H.extend,
            isFirefox = H.isFirefox,
            marginNames = H.marginNames,
            merge = H.merge,
            pick = H.pick,
            setAnimation = H.setAnimation,
            stableSort = H.stableSort,
            win = H.win,
            wrap = H.wrap;
        /**
         * The overview of the chart's series.
         * @class
         */
        Legend = H.Legend = function(chart, options) {
            this.init(chart, options);
        };

        Legend.prototype = {

            /**
             * Initialize the legend
             */
            init: function(chart, options) {

                this.chart = chart;

                this.setOptions(options);

                if (options.enabled) {

                    // Render it
                    this.render();

                    // move checkboxes
                    addEvent(this.chart, 'endResize', function() {
                        this.legend.positionCheckboxes();
                    });
                }
            },

            setOptions: function(options) {

                var padding = pick(options.padding, 8);

                this.options = options;


                this.itemStyle = options.itemStyle;
                this.itemHiddenStyle = merge(this.itemStyle, options.itemHiddenStyle);

                this.itemMarginTop = options.itemMarginTop || 0;
                this.padding = padding;
                this.initialItemX = padding;
                this.initialItemY = padding - 5; // 5 is the number of pixels above the text
                this.maxItemWidth = 0;
                this.itemHeight = 0;
                this.symbolWidth = pick(options.symbolWidth, 16);
                this.pages = [];

            },

            /**
             * Update the legend with new options. Equivalent to running chart.update with a legend
             * configuration option.
             * @param {Object} options Legend options
             * @param {Boolean} redraw Whether to redraw the chart, defaults to true.
             */
            update: function(options, redraw) {
                var chart = this.chart;

                this.setOptions(merge(true, this.options, options));
                this.destroy();
                chart.isDirtyLegend = chart.isDirtyBox = true;
                if (pick(redraw, true)) {
                    chart.redraw();
                }
            },

            /**
             * Set the colors for the legend item
             * @param {Object} item A Series or Point instance
             * @param {Object} visible Dimmed or colored
             */
            colorizeItem: function(item, visible) {
                item.legendGroup[visible ? 'removeClass' : 'addClass']('highcharts-legend-item-hidden');


                var legend = this,
                    options = legend.options,
                    legendItem = item.legendItem,
                    legendLine = item.legendLine,
                    legendSymbol = item.legendSymbol,
                    hiddenColor = legend.itemHiddenStyle.color,
                    textColor = visible ? options.itemStyle.color : hiddenColor,
                    symbolColor = visible ? (item.color || hiddenColor) : hiddenColor,
                    markerOptions = item.options && item.options.marker,
                    symbolAttr = {
                        fill: symbolColor
                    },
                    key;

                if (legendItem) {
                    legendItem.css({
                        fill: textColor,
                        color: textColor
                    }); // color for #1553, oldIE
                }
                if (legendLine) {
                    legendLine.attr({
                        stroke: symbolColor
                    });
                }

                if (legendSymbol) {

                    // Apply marker options
                    if (markerOptions && legendSymbol.isMarker) { // #585
                        //symbolAttr.stroke = symbolColor;
                        symbolAttr = item.pointAttribs();
                        if (!visible) {
                            for (key in symbolAttr) {
                                symbolAttr[key] = hiddenColor;
                            }
                        }
                    }

                    legendSymbol.attr(symbolAttr);
                }

            },

            /**
             * Position the legend item
             * @param {Object} item A Series or Point instance
             */
            positionItem: function(item) {
                var legend = this,
                    options = legend.options,
                    symbolPadding = options.symbolPadding,
                    ltr = !options.rtl,
                    legendItemPos = item._legendItemPos,
                    itemX = legendItemPos[0],
                    itemY = legendItemPos[1],
                    checkbox = item.checkbox,
                    legendGroup = item.legendGroup;

                if (legendGroup && legendGroup.element) {
                    legendGroup.translate(
                        ltr ? itemX : legend.legendWidth - itemX - 2 * symbolPadding - 4,
                        itemY
                    );
                }

                if (checkbox) {
                    checkbox.x = itemX;
                    checkbox.y = itemY;
                }
            },

            /**
             * Destroy a single legend item
             * @param {Object} item The series or point
             */
            destroyItem: function(item) {
                var checkbox = item.checkbox;

                // destroy SVG elements
                each(['legendItem', 'legendLine', 'legendSymbol', 'legendGroup'], function(key) {
                    if (item[key]) {
                        item[key] = item[key].destroy();
                    }
                });

                if (checkbox) {
                    discardElement(item.checkbox);
                }
            },

            /**
             * Destroys the legend.
             */
            destroy: function() {
                var legend = this,
                    legendGroup = legend.group,
                    box = legend.box;

                if (box) {
                    legend.box = box.destroy();
                }

                // Destroy items
                each(this.getAllItems(), function(item) {
                    each(['legendItem', 'legendGroup'], function(key) {
                        if (item[key]) {
                            item[key] = item[key].destroy();
                        }
                    });
                });

                if (legendGroup) {
                    legend.group = legendGroup.destroy();
                }
                legend.display = null; // Reset in .render on update.
            },

            /**
             * Position the checkboxes after the width is determined
             */
            positionCheckboxes: function(scrollOffset) {
                var alignAttr = this.group && this.group.alignAttr,
                    translateY,
                    clipHeight = this.clipHeight || this.legendHeight,
                    titleHeight = this.titleHeight;

                if (alignAttr) {
                    translateY = alignAttr.translateY;
                    each(this.allItems, function(item) {
                        var checkbox = item.checkbox,
                            top;

                        if (checkbox) {
                            top = translateY + titleHeight + checkbox.y + (scrollOffset || 0) + 3;
                            css(checkbox, {
                                left: (alignAttr.translateX + item.checkboxOffset + checkbox.x - 20) + 'px',
                                top: top + 'px',
                                display: top > translateY - 6 && top < translateY + clipHeight - 6 ? '' : 'none'
                            });
                        }
                    });
                }
            },

            /**
             * Render the legend title on top of the legend
             */
            renderTitle: function() {
                var options = this.options,
                    padding = this.padding,
                    titleOptions = options.title,
                    titleHeight = 0,
                    bBox;

                if (titleOptions.text) {
                    if (!this.title) {
                        this.title = this.chart.renderer.label(titleOptions.text, padding - 3, padding - 4, null, null, null, null, null, 'legend-title')
                            .attr({
                                zIndex: 1
                            })

                        .css(titleOptions.style)

                        .add(this.group);
                    }
                    bBox = this.title.getBBox();
                    titleHeight = bBox.height;
                    this.offsetWidth = bBox.width; // #1717
                    this.contentGroup.attr({
                        translateY: titleHeight
                    });
                }
                this.titleHeight = titleHeight;
            },

            /**
             * Set the legend item text
             */
            setText: function(item) {
                var options = this.options;
                item.legendItem.attr({
                    text: options.labelFormat ? H.format(options.labelFormat, item) : options.labelFormatter.call(item)
                });
            },

            /**
             * Render a single specific legend item
             * @param {Object} item A series or point
             */
            renderItem: function(item) {
                var legend = this,
                    chart = legend.chart,
                    renderer = chart.renderer,
                    options = legend.options,
                    horizontal = options.layout === 'horizontal',
                    symbolWidth = legend.symbolWidth,
                    symbolPadding = options.symbolPadding,

                    itemStyle = legend.itemStyle,
                    itemHiddenStyle = legend.itemHiddenStyle,

                    padding = legend.padding,
                    itemDistance = horizontal ? pick(options.itemDistance, 20) : 0,
                    ltr = !options.rtl,
                    itemHeight,
                    widthOption = options.width,
                    itemMarginBottom = options.itemMarginBottom || 0,
                    itemMarginTop = legend.itemMarginTop,
                    initialItemX = legend.initialItemX,
                    bBox,
                    itemWidth,
                    li = item.legendItem,
                    isSeries = !item.series,
                    series = !isSeries && item.series.drawLegendSymbol ? item.series : item,
                    seriesOptions = series.options,
                    showCheckbox = legend.createCheckboxForItem && seriesOptions && seriesOptions.showCheckbox,
                    useHTML = options.useHTML,
                    fontSize = 12;

                if (!li) { // generate it once, later move it

                    // Generate the group box
                    // A group to hold the symbol and text. Text is to be appended in Legend class.
                    item.legendGroup = renderer.g('legend-item')
                        .addClass('highcharts-' + series.type + '-series highcharts-color-' + item.colorIndex +
                            (item.options.className ? ' ' + item.options.className : '') +
                            (isSeries ? ' highcharts-series-' + item.index : '')
                        )
                        .attr({
                            zIndex: 1
                        })
                        .add(legend.scrollGroup);

                    // Generate the list item text and add it to the group
                    item.legendItem = li = renderer.text(
                        '',
                        ltr ? symbolWidth + symbolPadding : -symbolPadding,
                        legend.baseline || 0,
                        useHTML
                    )

                    .css(merge(item.visible ? itemStyle : itemHiddenStyle)) // merge to prevent modifying original (#1021)

                    .attr({
                            align: ltr ? 'left' : 'right',
                            zIndex: 2
                        })
                        .add(item.legendGroup);

                    // Get the baseline for the first item - the font size is equal for all
                    if (!legend.baseline) {

                        fontSize = itemStyle.fontSize;

                        legend.fontMetrics = renderer.fontMetrics(
                            fontSize,
                            li
                        );
                        legend.baseline = legend.fontMetrics.f + 3 + itemMarginTop;
                        li.attr('y', legend.baseline);
                    }

                    // Draw the legend symbol inside the group box
                    series.drawLegendSymbol(legend, item);

                    if (legend.setItemEvents) {
                        legend.setItemEvents(item, li, useHTML);
                    }

                    // add the HTML checkbox on top
                    if (showCheckbox) {
                        legend.createCheckboxForItem(item);
                    }
                }

                // Colorize the items
                legend.colorizeItem(item, item.visible);

                // Always update the text
                legend.setText(item);

                // calculate the positions for the next line
                bBox = li.getBBox();

                itemWidth = item.checkboxOffset =
                    options.itemWidth ||
                    item.legendItemWidth ||
                    symbolWidth + symbolPadding + bBox.width + itemDistance + (showCheckbox ? 20 : 0);
                legend.itemHeight = itemHeight = Math.round(item.legendItemHeight || bBox.height);

                // if the item exceeds the width, start a new line
                if (horizontal && legend.itemX - initialItemX + itemWidth >
                    (widthOption || (chart.chartWidth - 2 * padding - initialItemX - options.x))) {
                    legend.itemX = initialItemX;
                    legend.itemY += itemMarginTop + legend.lastLineHeight + itemMarginBottom;
                    legend.lastLineHeight = 0; // reset for next line (#915, #3976)
                }

                // If the item exceeds the height, start a new column
                /*if (!horizontal && legend.itemY + options.y + itemHeight > chart.chartHeight - spacingTop - spacingBottom) {
                	legend.itemY = legend.initialItemY;
                	legend.itemX += legend.maxItemWidth;
                	legend.maxItemWidth = 0;
                }*/

                // Set the edge positions
                legend.maxItemWidth = Math.max(legend.maxItemWidth, itemWidth);
                legend.lastItemY = itemMarginTop + legend.itemY + itemMarginBottom;
                legend.lastLineHeight = Math.max(itemHeight, legend.lastLineHeight); // #915

                // cache the position of the newly generated or reordered items
                item._legendItemPos = [legend.itemX, legend.itemY];

                // advance
                if (horizontal) {
                    legend.itemX += itemWidth;

                } else {
                    legend.itemY += itemMarginTop + itemHeight + itemMarginBottom;
                    legend.lastLineHeight = itemHeight;
                }

                // the width of the widest item
                legend.offsetWidth = widthOption || Math.max(
                    (horizontal ? legend.itemX - initialItemX - itemDistance : itemWidth) + padding,
                    legend.offsetWidth
                );
            },

            /**
             * Get all items, which is one item per series for normal series and one item per point
             * for pie series.
             */
            getAllItems: function() {
                var allItems = [];
                each(this.chart.series, function(series) {
                    var seriesOptions = series && series.options;

                    // Handle showInLegend. If the series is linked to another series, defaults to false.
                    if (series && pick(seriesOptions.showInLegend, !defined(seriesOptions.linkedTo) ? undefined : false, true)) {

                        // Use points or series for the legend item depending on legendType
                        allItems = allItems.concat(
                            series.legendItems ||
                            (seriesOptions.legendType === 'point' ?
                                series.data :
                                series)
                        );
                    }
                });
                return allItems;
            },

            /**
             * Adjust the chart margins by reserving space for the legend on only one side
             * of the chart. If the position is set to a corner, top or bottom is reserved
             * for horizontal legends and left or right for vertical ones.
             */
            adjustMargins: function(margin, spacing) {
                var chart = this.chart,
                    options = this.options,
                    // Use the first letter of each alignment option in order to detect the side
                    alignment = options.align.charAt(0) + options.verticalAlign.charAt(0) + options.layout.charAt(0); // #4189 - use charAt(x) notation instead of [x] for IE7

                if (!options.floating) {

                    each([
                        /(lth|ct|rth)/,
                        /(rtv|rm|rbv)/,
                        /(rbh|cb|lbh)/,
                        /(lbv|lm|ltv)/
                    ], function(alignments, side) {
                        if (alignments.test(alignment) && !defined(margin[side])) {
                            // Now we have detected on which side of the chart we should reserve space for the legend
                            chart[marginNames[side]] = Math.max(
                                chart[marginNames[side]],
                                chart.legend[(side + 1) % 2 ? 'legendHeight' : 'legendWidth'] + [1, -1, -1, 1][side] * options[(side % 2) ? 'x' : 'y'] +
                                pick(options.margin, 12) +
                                spacing[side]
                            );
                        }
                    });
                }
            },

            /**
             * Render the legend. This method can be called both before and after
             * chart.render. If called after, it will only rearrange items instead
             * of creating new ones.
             */
            render: function() {
                var legend = this,
                    chart = legend.chart,
                    renderer = chart.renderer,
                    legendGroup = legend.group,
                    allItems,
                    display,
                    legendWidth,
                    legendHeight,
                    box = legend.box,
                    options = legend.options,
                    padding = legend.padding;

                legend.itemX = legend.initialItemX;
                legend.itemY = legend.initialItemY;
                legend.offsetWidth = 0;
                legend.lastItemY = 0;

                if (!legendGroup) {
                    legend.group = legendGroup = renderer.g('legend')
                        .attr({
                            zIndex: 7
                        })
                        .add();
                    legend.contentGroup = renderer.g()
                        .attr({
                            zIndex: 1
                        }) // above background
                        .add(legendGroup);
                    legend.scrollGroup = renderer.g()
                        .add(legend.contentGroup);
                }

                legend.renderTitle();

                // add each series or point
                allItems = legend.getAllItems();

                // sort by legendIndex
                stableSort(allItems, function(a, b) {
                    return ((a.options && a.options.legendIndex) || 0) - ((b.options && b.options.legendIndex) || 0);
                });

                // reversed legend
                if (options.reversed) {
                    allItems.reverse();
                }

                legend.allItems = allItems;
                legend.display = display = !!allItems.length;

                // render the items
                legend.lastLineHeight = 0;
                each(allItems, function(item) {
                    legend.renderItem(item);
                });

                // Get the box
                legendWidth = (options.width || legend.offsetWidth) + padding;
                legendHeight = legend.lastItemY + legend.lastLineHeight + legend.titleHeight;
                legendHeight = legend.handleOverflow(legendHeight);
                legendHeight += padding;

                // Draw the border and/or background
                if (!box) {
                    legend.box = box = renderer.rect()
                        .addClass('highcharts-legend-box')
                        .attr({
                            r: options.borderRadius
                        })
                        .add(legendGroup);
                    box.isNew = true;
                }


                // Presentational
                box
                    .attr({
                        stroke: options.borderColor,
                        'stroke-width': options.borderWidth || 0,
                        fill: options.backgroundColor || 'none'
                    })
                    .shadow(options.shadow);


                if (legendWidth > 0 && legendHeight > 0) {
                    box[box.isNew ? 'attr' : 'animate'](
                        box.crisp({
                            x: 0,
                            y: 0,
                            width: legendWidth,
                            height: legendHeight
                        }, box.strokeWidth())
                    );
                    box.isNew = false;
                }

                // hide the border if no items
                box[display ? 'show' : 'hide']();



                legend.legendWidth = legendWidth;
                legend.legendHeight = legendHeight;

                // Now that the legend width and height are established, put the items in the
                // final position
                each(allItems, function(item) {
                    legend.positionItem(item);
                });

                // 1.x compatibility: positioning based on style
                /*var props = ['left', 'right', 'top', 'bottom'],
                	prop,
                	i = 4;
                while (i--) {
                	prop = props[i];
                	if (options.style[prop] && options.style[prop] !== 'auto') {
                		options[i < 2 ? 'align' : 'verticalAlign'] = prop;
                		options[i < 2 ? 'x' : 'y'] = pInt(options.style[prop]) * (i % 2 ? -1 : 1);
                	}
                }*/

                if (display) {
                    legendGroup.align(extend({
                        width: legendWidth,
                        height: legendHeight
                    }, options), true, 'spacingBox');
                }

                if (!chart.isResizing) {
                    this.positionCheckboxes();
                }
            },

            /**
             * Set up the overflow handling by adding navigation with up and down arrows below the
             * legend.
             */
            handleOverflow: function(legendHeight) {
                var legend = this,
                    chart = this.chart,
                    renderer = chart.renderer,
                    options = this.options,
                    optionsY = options.y,
                    alignTop = options.verticalAlign === 'top',
                    spaceHeight = chart.spacingBox.height + (alignTop ? -optionsY : optionsY) - this.padding,
                    maxHeight = options.maxHeight,
                    clipHeight,
                    clipRect = this.clipRect,
                    navOptions = options.navigation,
                    animation = pick(navOptions.animation, true),
                    arrowSize = navOptions.arrowSize || 12,
                    nav = this.nav,
                    pages = this.pages,
                    padding = this.padding,
                    lastY,
                    allItems = this.allItems,
                    clipToHeight = function(height) {
                        if (height) {
                            clipRect.attr({
                                height: height
                            });
                        } else if (clipRect) { // Reset (#5912)
                            legend.clipRect = clipRect.destroy();
                            legend.contentGroup.clip();
                        }

                        // useHTML
                        if (legend.contentGroup.div) {
                            legend.contentGroup.div.style.clip = height ?
                                'rect(' + padding + 'px,9999px,' +
                                (padding + height) + 'px,0)' :
                                'auto';
                        }
                    };


                // Adjust the height
                if (options.layout === 'horizontal' && options.verticalAlign !== 'middle' && !options.floating) {
                    spaceHeight /= 2;
                }
                if (maxHeight) {
                    spaceHeight = Math.min(spaceHeight, maxHeight);
                }

                // Reset the legend height and adjust the clipping rectangle
                pages.length = 0;
                if (legendHeight > spaceHeight && navOptions.enabled !== false) {

                    this.clipHeight = clipHeight = Math.max(spaceHeight - 20 - this.titleHeight - padding, 0);
                    this.currentPage = pick(this.currentPage, 1);
                    this.fullHeight = legendHeight;

                    // Fill pages with Y positions so that the top of each a legend item defines
                    // the scroll top for each page (#2098)
                    each(allItems, function(item, i) {
                        var y = item._legendItemPos[1],
                            h = Math.round(item.legendItem.getBBox().height),
                            len = pages.length;

                        if (!len || (y - pages[len - 1] > clipHeight && (lastY || y) !== pages[len - 1])) {
                            pages.push(lastY || y);
                            len++;
                        }

                        if (i === allItems.length - 1 && y + h - pages[len - 1] > clipHeight) {
                            pages.push(y);
                        }
                        if (y !== lastY) {
                            lastY = y;
                        }
                    });

                    // Only apply clipping if needed. Clipping causes blurred legend in PDF export (#1787)
                    if (!clipRect) {
                        clipRect = legend.clipRect = renderer.clipRect(0, padding, 9999, 0);
                        legend.contentGroup.clip(clipRect);
                    }

                    clipToHeight(clipHeight);

                    // Add navigation elements
                    if (!nav) {
                        this.nav = nav = renderer.g().attr({
                            zIndex: 1
                        }).add(this.group);
                        this.up = renderer.symbol('triangle', 0, 0, arrowSize, arrowSize)
                            .on('click', function() {
                                legend.scroll(-1, animation);
                            })
                            .add(nav);
                        this.pager = renderer.text('', 15, 10)
                            .addClass('highcharts-legend-navigation')

                        .css(navOptions.style)

                        .add(nav);
                        this.down = renderer.symbol('triangle-down', 0, 0, arrowSize, arrowSize)
                            .on('click', function() {
                                legend.scroll(1, animation);
                            })
                            .add(nav);
                    }

                    // Set initial position
                    legend.scroll(0);

                    legendHeight = spaceHeight;

                    // Reset
                } else if (nav) {
                    clipToHeight();
                    nav.hide();
                    this.scrollGroup.attr({
                        translateY: 1
                    });
                    this.clipHeight = 0; // #1379
                }

                return legendHeight;
            },

            /**
             * Scroll the legend by a number of pages
             * @param {Object} scrollBy
             * @param {Object} animation
             */
            scroll: function(scrollBy, animation) {
                var pages = this.pages,
                    pageCount = pages.length,
                    currentPage = this.currentPage + scrollBy,
                    clipHeight = this.clipHeight,
                    navOptions = this.options.navigation,
                    pager = this.pager,
                    padding = this.padding,
                    scrollOffset;

                // When resizing while looking at the last page
                if (currentPage > pageCount) {
                    currentPage = pageCount;
                }

                if (currentPage > 0) {

                    if (animation !== undefined) {
                        setAnimation(animation, this.chart);
                    }

                    this.nav.attr({
                        translateX: padding,
                        translateY: clipHeight + this.padding + 7 + this.titleHeight,
                        visibility: 'visible'
                    });
                    this.up.attr({
                        'class': currentPage === 1 ? 'highcharts-legend-nav-inactive' : 'highcharts-legend-nav-active'
                    });
                    pager.attr({
                        text: currentPage + '/' + pageCount
                    });
                    this.down.attr({
                        'x': 18 + this.pager.getBBox().width, // adjust to text width
                        'class': currentPage === pageCount ? 'highcharts-legend-nav-inactive' : 'highcharts-legend-nav-active'
                    });


                    this.up
                        .attr({
                            fill: currentPage === 1 ? navOptions.inactiveColor : navOptions.activeColor
                        })
                        .css({
                            cursor: currentPage === 1 ? 'default' : 'pointer'
                        });
                    this.down
                        .attr({
                            fill: currentPage === pageCount ? navOptions.inactiveColor : navOptions.activeColor
                        })
                        .css({
                            cursor: currentPage === pageCount ? 'default' : 'pointer'
                        });


                    scrollOffset = -pages[currentPage - 1] + this.initialItemY;

                    this.scrollGroup.animate({
                        translateY: scrollOffset
                    });

                    this.currentPage = currentPage;
                    this.positionCheckboxes(scrollOffset);
                }

            }

        };

        /*
         * LegendSymbolMixin
         */

        H.LegendSymbolMixin = {

            /**
             * Get the series' symbol in the legend
             *
             * @param {Object} legend The legend object
             * @param {Object} item The series (this) or point
             */
            drawRectangle: function(legend, item) {
                var options = legend.options,
                    symbolHeight = options.symbolHeight || legend.fontMetrics.f,
                    square = options.squareSymbol,
                    symbolWidth = square ? symbolHeight : legend.symbolWidth;

                item.legendSymbol = this.chart.renderer.rect(
                        square ? (legend.symbolWidth - symbolHeight) / 2 : 0,
                        legend.baseline - symbolHeight + 1, // #3988
                        symbolWidth,
                        symbolHeight,
                        pick(legend.options.symbolRadius, symbolHeight / 2)
                    )
                    .addClass('highcharts-point')
                    .attr({
                        zIndex: 3
                    }).add(item.legendGroup);

            },

            /**
             * Get the series' symbol in the legend. This method should be overridable to create custom
             * symbols through Highcharts.seriesTypes[type].prototype.drawLegendSymbols.
             *
             * @param {Object} legend The legend object
             */
            drawLineMarker: function(legend) {

                var options = this.options,
                    markerOptions = options.marker,
                    radius,
                    legendSymbol,
                    symbolWidth = legend.symbolWidth,
                    renderer = this.chart.renderer,
                    legendItemGroup = this.legendGroup,
                    verticalCenter = legend.baseline - Math.round(legend.fontMetrics.b * 0.3),
                    attr = {};

                // Draw the line

                attr = {
                    'stroke-width': options.lineWidth || 0
                };
                if (options.dashStyle) {
                    attr.dashstyle = options.dashStyle;
                }


                this.legendLine = renderer.path([
                        'M',
                        0,
                        verticalCenter,
                        'L',
                        symbolWidth,
                        verticalCenter
                    ])
                    .addClass('highcharts-graph')
                    .attr(attr)
                    .add(legendItemGroup);

                // Draw the marker
                if (markerOptions && markerOptions.enabled !== false) {
                    radius = this.symbol.indexOf('url') === 0 ? 0 : markerOptions.radius;
                    this.legendSymbol = legendSymbol = renderer.symbol(
                            this.symbol,
                            (symbolWidth / 2) - radius,
                            verticalCenter - radius,
                            2 * radius,
                            2 * radius,
                            markerOptions
                        )
                        .addClass('highcharts-point')
                        .add(legendItemGroup);
                    legendSymbol.isMarker = true;
                }
            }
        };

        // Workaround for #2030, horizontal legend items not displaying in IE11 Preview,
        // and for #2580, a similar drawing flaw in Firefox 26.
        // Explore if there's a general cause for this. The problem may be related
        // to nested group elements, as the legend item texts are within 4 group elements.
        if (/Trident\/7\.0/.test(win.navigator.userAgent) || isFirefox) {
            wrap(Legend.prototype, 'positionItem', function(proceed, item) {
                var legend = this,
                    runPositionItem = function() { // If chart destroyed in sync, this is undefined (#2030)
                        if (item._legendItemPos) {
                            proceed.call(legend, item);
                        }
                    };

                // Do it now, for export and to get checkbox placement
                runPositionItem();

                // Do it after to work around the core issue
                setTimeout(runPositionItem);
            });
        }

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var addEvent = H.addEvent,
            animate = H.animate,
            animObject = H.animObject,
            attr = H.attr,
            doc = H.doc,
            Axis = H.Axis, // @todo add as requirement
            createElement = H.createElement,
            defaultOptions = H.defaultOptions,
            discardElement = H.discardElement,
            charts = H.charts,
            css = H.css,
            defined = H.defined,
            each = H.each,
            error = H.error,
            extend = H.extend,
            fireEvent = H.fireEvent,
            getStyle = H.getStyle,
            grep = H.grep,
            isNumber = H.isNumber,
            isObject = H.isObject,
            isString = H.isString,
            Legend = H.Legend, // @todo add as requirement
            marginNames = H.marginNames,
            merge = H.merge,
            Pointer = H.Pointer, // @todo add as requirement
            pick = H.pick,
            pInt = H.pInt,
            removeEvent = H.removeEvent,
            seriesTypes = H.seriesTypes,
            splat = H.splat,
            svg = H.svg,
            syncTimeout = H.syncTimeout,
            win = H.win,
            Renderer = H.Renderer;
        /**
         * The Chart class.
         * @class Highcharts.Chart
         * @memberOf Highcharts
         * @param {String|HTMLDOMElement} renderTo - The DOM element to render to, or its
         * id.
         * @param {ChartOptions} options - The chart options structure.
         * @param {Function} callback - Function to run when the chart has loaded.
         */
        var Chart = H.Chart = function() {
            this.getArgs.apply(this, arguments);
        };

        H.chart = function(a, b, c) {
            return new Chart(a, b, c);
        };

        Chart.prototype = {

            /**
             * Hook for modules
             */
            callbacks: [],

            /**
             * Handle the arguments passed to the constructor
             * @returns {Array} Arguments without renderTo
             */
            getArgs: function() {
                var args = [].slice.call(arguments);

                // Remove the optional first argument, renderTo, and
                // set it on this.
                if (isString(args[0]) || args[0].nodeName) {
                    this.renderTo = args.shift();
                }
                this.init(args[0], args[1]);
            },

            /**
             * Initialize the chart
             */
            init: function(userOptions, callback) {

                // Handle regular options
                var options,
                    seriesOptions = userOptions.series; // skip merging data points to increase performance

                userOptions.series = null;
                options = merge(defaultOptions, userOptions); // do the merge
                options.series = userOptions.series = seriesOptions; // set back the series data
                this.userOptions = userOptions;
                this.respRules = [];

                var optionsChart = options.chart;

                var chartEvents = optionsChart.events;

                this.margin = [];
                this.spacing = [];

                //this.runChartClick = chartEvents && !!chartEvents.click;
                this.bounds = {
                    h: {},
                    v: {}
                }; // Pixel data bounds for touch zoom

                this.callback = callback;
                this.isResizing = 0;
                this.options = options;
                //chartTitleOptions = undefined;
                //chartSubtitleOptions = undefined;

                this.axes = [];
                this.series = [];
                this.hasCartesianSeries = optionsChart.showAxes;
                //this.axisOffset = undefined;
                //this.inverted = undefined;
                //this.loadingShown = undefined;
                //this.container = undefined;
                //this.chartWidth = undefined;
                //this.chartHeight = undefined;
                //this.marginRight = undefined;
                //this.marginBottom = undefined;
                //this.containerWidth = undefined;
                //this.containerHeight = undefined;
                //this.oldChartWidth = undefined;
                //this.oldChartHeight = undefined;

                //this.renderTo = undefined;
                //this.renderToClone = undefined;

                //this.spacingBox = undefined

                //this.legend = undefined;

                // Elements
                //this.chartBackground = undefined;
                //this.plotBackground = undefined;
                //this.plotBGImage = undefined;
                //this.plotBorder = undefined;
                //this.loadingDiv = undefined;
                //this.loadingSpan = undefined;

                var chart = this,
                    eventType;

                // Add the chart to the global lookup
                chart.index = charts.length;
                charts.push(chart);
                H.chartCount++;

                // Chart event handlers
                if (chartEvents) {
                    for (eventType in chartEvents) {
                        addEvent(chart, eventType, chartEvents[eventType]);
                    }
                }

                chart.xAxis = [];
                chart.yAxis = [];

                chart.pointCount = chart.colorCounter = chart.symbolCounter = 0;

                chart.firstRender();
            },

            /**
             * Initialize an individual series, called internally before render time
             */
            initSeries: function(options) {
                var chart = this,
                    optionsChart = chart.options.chart,
                    type = options.type || optionsChart.type || optionsChart.defaultSeriesType,
                    series,
                    Constr = seriesTypes[type];

                // No such series type
                if (!Constr) {
                    error(17, true);
                }

                series = new Constr();
                series.init(this, options);
                return series;
            },

            /**
             * Check whether a given point is within the plot area
             *
             * @param {Number} plotX Pixel x relative to the plot area
             * @param {Number} plotY Pixel y relative to the plot area
             * @param {Boolean} inverted Whether the chart is inverted
             */
            isInsidePlot: function(plotX, plotY, inverted) {
                var x = inverted ? plotY : plotX,
                    y = inverted ? plotX : plotY;

                return x >= 0 &&
                    x <= this.plotWidth &&
                    y >= 0 &&
                    y <= this.plotHeight;
            },

            /**
             * Redraw legend, axes or series based on updated data
             *
             * @param {Boolean|Object} animation Whether to apply animation, and optionally animation
             *	configuration
             */
            redraw: function(animation) {
                var chart = this,
                    axes = chart.axes,
                    series = chart.series,
                    pointer = chart.pointer,
                    legend = chart.legend,
                    redrawLegend = chart.isDirtyLegend,
                    hasStackedSeries,
                    hasDirtyStacks,
                    hasCartesianSeries = chart.hasCartesianSeries,
                    isDirtyBox = chart.isDirtyBox,
                    seriesLength = series.length,
                    i = seriesLength,
                    serie,
                    renderer = chart.renderer,
                    isHiddenChart = renderer.isHidden(),
                    afterRedraw = [];

                H.setAnimation(animation, chart);

                if (isHiddenChart) {
                    chart.cloneRenderTo();
                }

                // Adjust title layout (reflow multiline text)
                chart.layOutTitles();

                // link stacked series
                while (i--) {
                    serie = series[i];

                    if (serie.options.stacking) {
                        hasStackedSeries = true;

                        if (serie.isDirty) {
                            hasDirtyStacks = true;
                            break;
                        }
                    }
                }
                if (hasDirtyStacks) { // mark others as dirty
                    i = seriesLength;
                    while (i--) {
                        serie = series[i];
                        if (serie.options.stacking) {
                            serie.isDirty = true;
                        }
                    }
                }

                // Handle updated data in the series
                each(series, function(serie) {
                    if (serie.isDirty) {
                        if (serie.options.legendType === 'point') {
                            if (serie.updateTotals) {
                                serie.updateTotals();
                            }
                            redrawLegend = true;
                        }
                    }
                    if (serie.isDirtyData) {
                        fireEvent(serie, 'updatedData');
                    }
                });

                // handle added or removed series
                if (redrawLegend && legend.options.enabled) { // series or pie points are added or removed
                    // draw legend graphics
                    legend.render();

                    chart.isDirtyLegend = false;
                }

                // reset stacks
                if (hasStackedSeries) {
                    chart.getStacks();
                }


                if (hasCartesianSeries) {
                    // set axes scales
                    each(axes, function(axis) {
                        axis.updateNames();
                        axis.setScale();
                    });
                }

                chart.getMargins(); // #3098

                if (hasCartesianSeries) {
                    // If one axis is dirty, all axes must be redrawn (#792, #2169)
                    each(axes, function(axis) {
                        if (axis.isDirty) {
                            isDirtyBox = true;
                        }
                    });

                    // redraw axes
                    each(axes, function(axis) {

                        // Fire 'afterSetExtremes' only if extremes are set
                        var key = axis.min + ',' + axis.max;
                        if (axis.extKey !== key) { // #821, #4452
                            axis.extKey = key;
                            afterRedraw.push(function() { // prevent a recursive call to chart.redraw() (#1119)
                                fireEvent(axis, 'afterSetExtremes', extend(axis.eventArgs, axis.getExtremes())); // #747, #751
                                delete axis.eventArgs;
                            });
                        }
                        if (isDirtyBox || hasStackedSeries) {
                            axis.redraw();
                        }
                    });
                }

                // the plot areas size has changed
                if (isDirtyBox) {
                    chart.drawChartBox();
                }


                // redraw affected series
                each(series, function(serie) {
                    if ((isDirtyBox || serie.isDirty) && serie.visible) {
                        serie.redraw();
                    }
                });

                // move tooltip or reset
                if (pointer) {
                    pointer.reset(true);
                }

                // redraw if canvas
                renderer.draw();

                // fire the event
                fireEvent(chart, 'redraw');

                if (isHiddenChart) {
                    chart.cloneRenderTo(true);
                }

                // Fire callbacks that are put on hold until after the redraw
                each(afterRedraw, function(callback) {
                    callback.call();
                });
            },

            /**
             * Get an axis, series or point object by id.
             * @param id {String} The id as given in the configuration options
             */
            get: function(id) {
                var chart = this,
                    axes = chart.axes,
                    series = chart.series;

                var i,
                    j,
                    points;

                // search axes
                for (i = 0; i < axes.length; i++) {
                    if (axes[i].options.id === id) {
                        return axes[i];
                    }
                }

                // search series
                for (i = 0; i < series.length; i++) {
                    if (series[i].options.id === id) {
                        return series[i];
                    }
                }

                // search points
                for (i = 0; i < series.length; i++) {
                    points = series[i].points || [];
                    for (j = 0; j < points.length; j++) {
                        if (points[j].id === id) {
                            return points[j];
                        }
                    }
                }
                return null;
            },

            /**
             * Create the Axis instances based on the config options
             */
            getAxes: function() {
                var chart = this,
                    options = this.options,
                    xAxisOptions = options.xAxis = splat(options.xAxis || {}),
                    yAxisOptions = options.yAxis = splat(options.yAxis || {}),
                    optionsArray;

                // make sure the options are arrays and add some members
                each(xAxisOptions, function(axis, i) {
                    axis.index = i;
                    axis.isX = true;
                });

                each(yAxisOptions, function(axis, i) {
                    axis.index = i;
                });

                // concatenate all axis options into one array
                optionsArray = xAxisOptions.concat(yAxisOptions);

                each(optionsArray, function(axisOptions) {
                    new Axis(chart, axisOptions); // eslint-disable-line no-new
                });
            },


            /**
             * Get the currently selected points from all series
             */
            getSelectedPoints: function() {
                var points = [];
                each(this.series, function(serie) {
                    points = points.concat(grep(serie.points || [], function(point) {
                        return point.selected;
                    }));
                });
                return points;
            },

            /**
             * Get the currently selected series
             */
            getSelectedSeries: function() {
                return grep(this.series, function(serie) {
                    return serie.selected;
                });
            },

            /**
             * Show the title and subtitle of the chart
             *
             * @param titleOptions {Object} New title options
             * @param subtitleOptions {Object} New subtitle options
             *
             */
            setTitle: function(titleOptions, subtitleOptions, redraw) {
                var chart = this,
                    options = chart.options,
                    chartTitleOptions,
                    chartSubtitleOptions;

                chartTitleOptions = options.title = merge(options.title, titleOptions);
                chartSubtitleOptions = options.subtitle = merge(options.subtitle, subtitleOptions);

                // add title and subtitle
                each([
                    ['title', titleOptions, chartTitleOptions],
                    ['subtitle', subtitleOptions, chartSubtitleOptions]
                ], function(arr, i) {
                    var name = arr[0],
                        title = chart[name],
                        titleOptions = arr[1],
                        chartTitleOptions = arr[2];

                    if (title && titleOptions) {
                        chart[name] = title = title.destroy(); // remove old
                    }

                    if (chartTitleOptions && chartTitleOptions.text && !title) {
                        chart[name] = chart.renderer.text(
                                chartTitleOptions.text,
                                0,
                                0,
                                chartTitleOptions.useHTML
                            )
                            .attr({
                                align: chartTitleOptions.align,
                                'class': 'highcharts-' + name,
                                zIndex: chartTitleOptions.zIndex || 4
                            })
                            .add();

                        // Update methods, shortcut to Chart.setTitle
                        chart[name].update = function(o) {
                            chart.setTitle(!i && o, i && o);
                        };


                        // Presentational
                        chart[name].css(chartTitleOptions.style);


                    }
                });
                chart.layOutTitles(redraw);
            },

            /**
             * Lay out the chart titles and cache the full offset height for use in getMargins
             */
            layOutTitles: function(redraw) {
                var titleOffset = 0,
                    requiresDirtyBox,
                    renderer = this.renderer,
                    spacingBox = this.spacingBox;

                // Lay out the title and the subtitle respectively
                each(['title', 'subtitle'], function(key) {
                    var title = this[key],
                        titleOptions = this.options[key],
                        titleSize;

                    if (title) {

                        titleSize = titleOptions.style.fontSize;

                        titleSize = renderer.fontMetrics(titleSize, title).b;

                        title
                            .css({
                                width: (titleOptions.width || spacingBox.width + titleOptions.widthAdjust) + 'px'
                            })
                            .align(extend({
                                y: titleOffset + titleSize + (key === 'title' ? -3 : 2)
                            }, titleOptions), false, 'spacingBox');

                        if (!titleOptions.floating && !titleOptions.verticalAlign) {
                            titleOffset = Math.ceil(titleOffset + title.getBBox().height);
                        }
                    }
                }, this);

                requiresDirtyBox = this.titleOffset !== titleOffset;
                this.titleOffset = titleOffset; // used in getMargins

                if (!this.isDirtyBox && requiresDirtyBox) {
                    this.isDirtyBox = requiresDirtyBox;
                    // Redraw if necessary (#2719, #2744)
                    if (this.hasRendered && pick(redraw, true) && this.isDirtyBox) {
                        this.redraw();
                    }
                }
            },

            /**
             * Get chart width and height according to options and container size
             */
            getChartSize: function() {
                var chart = this,
                    optionsChart = chart.options.chart,
                    widthOption = optionsChart.width,
                    heightOption = optionsChart.height,
                    renderTo = chart.renderToClone || chart.renderTo;

                // Get inner width and height
                if (!defined(widthOption)) {
                    chart.containerWidth = getStyle(renderTo, 'width');
                }
                if (!defined(heightOption)) {
                    chart.containerHeight = getStyle(renderTo, 'height');
                }

                chart.chartWidth = Math.max(0, widthOption || chart.containerWidth || 600); // #1393, 1460
                chart.chartHeight = Math.max(0, pick(heightOption,
                    // the offsetHeight of an empty container is 0 in standard browsers, but 19 in IE7:
                    chart.containerHeight > 19 ? chart.containerHeight : 400));
            },

            /**
             * Create a clone of the chart's renderTo div and place it outside the viewport to allow
             * size computation on chart.render and chart.redraw
             */
            cloneRenderTo: function(revert) {
                var clone = this.renderToClone,
                    container = this.container;

                // Destroy the clone and bring the container back to the real renderTo div
                if (revert) {
                    if (clone) {
                        while (clone.childNodes.length) { // #5231
                            this.renderTo.appendChild(clone.firstChild);
                        }
                        discardElement(clone);
                        delete this.renderToClone;
                    }

                    // Set up the clone
                } else {
                    if (container && container.parentNode === this.renderTo) {
                        this.renderTo.removeChild(container); // do not clone this
                    }
                    this.renderToClone = clone = this.renderTo.cloneNode(0);
                    css(clone, {
                        position: 'absolute',
                        top: '-9999px',
                        display: 'block' // #833
                    });
                    if (clone.style.setProperty) { // #2631
                        clone.style.setProperty('display', 'block', 'important');
                    }
                    doc.body.appendChild(clone);
                    if (container) {
                        clone.appendChild(container);
                    }
                }
            },

            /**
             * Setter for the chart class name
             */
            setClassName: function(className) {
                this.container.className = 'highcharts-container ' + (className || '');
            },

            /**
             * Get the containing element, determine the size and create the inner container
             * div to hold the chart
             */
            getContainer: function() {
                var chart = this,
                    container,
                    options = chart.options,
                    optionsChart = options.chart,
                    chartWidth,
                    chartHeight,
                    renderTo = chart.renderTo,
                    indexAttrName = 'data-highcharts-chart',
                    oldChartIndex,
                    Ren,
                    containerId = H.uniqueKey(),
                    containerStyle,
                    key;

                if (!renderTo) {
                    chart.renderTo = renderTo = optionsChart.renderTo;
                }

                if (isString(renderTo)) {
                    chart.renderTo = renderTo = doc.getElementById(renderTo);
                }

                // Display an error if the renderTo is wrong
                if (!renderTo) {
                    error(13, true);
                }

                // If the container already holds a chart, destroy it. The check for hasRendered is there
                // because web pages that are saved to disk from the browser, will preserve the data-highcharts-chart
                // attribute and the SVG contents, but not an interactive chart. So in this case,
                // charts[oldChartIndex] will point to the wrong chart if any (#2609).
                oldChartIndex = pInt(attr(renderTo, indexAttrName));
                if (isNumber(oldChartIndex) && charts[oldChartIndex] && charts[oldChartIndex].hasRendered) {
                    charts[oldChartIndex].destroy();
                }

                // Make a reference to the chart from the div
                attr(renderTo, indexAttrName, chart.index);

                // remove previous chart
                renderTo.innerHTML = '';

                // If the container doesn't have an offsetWidth, it has or is a child of
                // a node that has display:none. We need to temporarily move it out to a
                // visible state to determine the size, else the legend and tooltips
                // won't render properly. The skipClone option is used in sparklines as
                // a micro optimization, saving about 1-2 ms each chart.
                if (!optionsChart.skipClone && !renderTo.offsetWidth) {
                    chart.cloneRenderTo();
                }

                // get the width and height
                chart.getChartSize();
                chartWidth = chart.chartWidth;
                chartHeight = chart.chartHeight;

                // Create the inner container

                containerStyle = extend({
                    position: 'relative',
                    overflow: 'hidden', // needed for context menu (avoid scrollbars) and
                    // content overflow in IE
                    width: chartWidth + 'px',
                    height: chartHeight + 'px',
                    textAlign: 'left',
                    lineHeight: 'normal', // #427
                    zIndex: 0, // #1072
                    '-webkit-tap-highlight-color': 'rgba(0,0,0,0)'
                }, optionsChart.style);

                chart.container = container = createElement(
                    'div', {
                        id: containerId
                    },
                    containerStyle,
                    chart.renderToClone || renderTo
                );

                // cache the cursor (#1650)
                chart._cursor = container.style.cursor;

                // Initialize the renderer
                Ren = H[optionsChart.renderer] || Renderer;
                chart.renderer = new Ren(
                    container,
                    chartWidth,
                    chartHeight,
                    null,
                    optionsChart.forExport,
                    options.exporting && options.exporting.allowHTML
                );


                chart.setClassName(optionsChart.className);

                chart.renderer.setStyle(optionsChart.style);


                // Add a reference to the charts index
                chart.renderer.chartIndex = chart.index;
            },

            /**
             * Calculate margins by rendering axis labels in a preliminary position. Title,
             * subtitle and legend have already been rendered at this stage, but will be
             * moved into their final positions
             */
            getMargins: function(skipAxes) {
                var chart = this,
                    spacing = chart.spacing,
                    margin = chart.margin,
                    titleOffset = chart.titleOffset;

                chart.resetMargins();

                // Adjust for title and subtitle
                if (titleOffset && !defined(margin[0])) {
                    chart.plotTop = Math.max(chart.plotTop, titleOffset + chart.options.title.margin + spacing[0]);
                }

                // Adjust for legend
                if (chart.legend.display) {
                    chart.legend.adjustMargins(margin, spacing);
                }

                // adjust for scroller
                if (chart.extraBottomMargin) {
                    chart.marginBottom += chart.extraBottomMargin;
                }
                if (chart.extraTopMargin) {
                    chart.plotTop += chart.extraTopMargin;
                }
                if (!skipAxes) {
                    this.getAxisMargins();
                }
            },

            getAxisMargins: function() {

                var chart = this,
                    axisOffset = chart.axisOffset = [0, 0, 0, 0], // top, right, bottom, left
                    margin = chart.margin;

                // pre-render axes to get labels offset width
                if (chart.hasCartesianSeries) {
                    each(chart.axes, function(axis) {
                        if (axis.visible) {
                            axis.getOffset();
                        }
                    });
                }

                // Add the axis offsets
                each(marginNames, function(m, side) {
                    if (!defined(margin[side])) {
                        chart[m] += axisOffset[side];
                    }
                });

                chart.setChartSize();

            },

            /**
             * Resize the chart to its container if size is not explicitly set
             */
            reflow: function(e) {
                var chart = this,
                    optionsChart = chart.options.chart,
                    renderTo = chart.renderTo,
                    hasUserWidth = defined(optionsChart.width),
                    width = optionsChart.width || getStyle(renderTo, 'width'),
                    height = optionsChart.height || getStyle(renderTo, 'height'),
                    target = e ? e.target : win;

                // Width and height checks for display:none. Target is doc in IE8 and Opera,
                // win in Firefox, Chrome and IE9.
                if (!hasUserWidth && !chart.isPrinting && width && height && (target === win || target === doc)) { // #1093
                    if (width !== chart.containerWidth || height !== chart.containerHeight) {
                        clearTimeout(chart.reflowTimeout);
                        // When called from window.resize, e is set, else it's called directly (#2224)
                        chart.reflowTimeout = syncTimeout(function() {
                            if (chart.container) { // It may have been destroyed in the meantime (#1257)
                                chart.setSize(undefined, undefined, false);
                            }
                        }, e ? 100 : 0);
                    }
                    chart.containerWidth = width;
                    chart.containerHeight = height;
                }
            },

            /**
             * Add the event handlers necessary for auto resizing
             */
            initReflow: function() {
                var chart = this,
                    unbind;

                unbind = addEvent(win, 'resize', function(e) {
                    chart.reflow(e);
                });
                addEvent(chart, 'destroy', unbind);

                // The following will add listeners to re-fit the chart before and after
                // printing (#2284). However it only works in WebKit. Should have worked
                // in Firefox, but not supported in IE.
                /*
                if (win.matchMedia) {
                	win.matchMedia('print').addListener(function reflow() {
                		chart.reflow();
                	});
                }
                */
            },

            /**
             * Resize the chart to a given width and height
             * @param {Number} width
             * @param {Number} height
             * @param {Object|Boolean} animation
             */
            setSize: function(width, height, animation) {
                var chart = this,
                    renderer = chart.renderer,
                    globalAnimation;

                // Handle the isResizing counter
                chart.isResizing += 1;

                // set the animation for the current process
                H.setAnimation(animation, chart);

                chart.oldChartHeight = chart.chartHeight;
                chart.oldChartWidth = chart.chartWidth;
                if (width !== undefined) {
                    chart.options.chart.width = width;
                }
                if (height !== undefined) {
                    chart.options.chart.height = height;
                }
                chart.getChartSize();

                // Resize the container with the global animation applied if enabled (#2503)

                globalAnimation = renderer.globalAnimation;
                (globalAnimation ? animate : css)(chart.container, {
                    width: chart.chartWidth + 'px',
                    height: chart.chartHeight + 'px'
                }, globalAnimation);


                chart.setChartSize(true);
                renderer.setSize(chart.chartWidth, chart.chartHeight, animation);

                // handle axes
                each(chart.axes, function(axis) {
                    axis.isDirty = true;
                    axis.setScale();
                });

                chart.isDirtyLegend = true; // force legend redraw
                chart.isDirtyBox = true; // force redraw of plot and chart border

                chart.layOutTitles(); // #2857
                chart.getMargins();

                if (chart.setResponsive) {
                    chart.setResponsive(false);
                }
                chart.redraw(animation);


                chart.oldChartHeight = null;
                fireEvent(chart, 'resize');

                // Fire endResize and set isResizing back. If animation is disabled, fire without delay
                syncTimeout(function() {
                    if (chart) {
                        fireEvent(chart, 'endResize', null, function() {
                            chart.isResizing -= 1;
                        });
                    }
                }, animObject(globalAnimation).duration);
            },

            /**
             * Set the public chart properties. This is done before and after the pre-render
             * to determine margin sizes
             */
            setChartSize: function(skipAxes) {
                var chart = this,
                    inverted = chart.inverted,
                    renderer = chart.renderer,
                    chartWidth = chart.chartWidth,
                    chartHeight = chart.chartHeight,
                    optionsChart = chart.options.chart,
                    spacing = chart.spacing,
                    clipOffset = chart.clipOffset,
                    clipX,
                    clipY,
                    plotLeft,
                    plotTop,
                    plotWidth,
                    plotHeight,
                    plotBorderWidth;

                chart.plotLeft = plotLeft = Math.round(chart.plotLeft);
                chart.plotTop = plotTop = Math.round(chart.plotTop);
                chart.plotWidth = plotWidth = Math.max(0, Math.round(chartWidth - plotLeft - chart.marginRight));
                chart.plotHeight = plotHeight = Math.max(0, Math.round(chartHeight - plotTop - chart.marginBottom));

                chart.plotSizeX = inverted ? plotHeight : plotWidth;
                chart.plotSizeY = inverted ? plotWidth : plotHeight;

                chart.plotBorderWidth = optionsChart.plotBorderWidth || 0;

                // Set boxes used for alignment
                chart.spacingBox = renderer.spacingBox = {
                    x: spacing[3],
                    y: spacing[0],
                    width: chartWidth - spacing[3] - spacing[1],
                    height: chartHeight - spacing[0] - spacing[2]
                };
                chart.plotBox = renderer.plotBox = {
                    x: plotLeft,
                    y: plotTop,
                    width: plotWidth,
                    height: plotHeight
                };

                plotBorderWidth = 2 * Math.floor(chart.plotBorderWidth / 2);
                clipX = Math.ceil(Math.max(plotBorderWidth, clipOffset[3]) / 2);
                clipY = Math.ceil(Math.max(plotBorderWidth, clipOffset[0]) / 2);
                chart.clipBox = {
                    x: clipX,
                    y: clipY,
                    width: Math.floor(chart.plotSizeX - Math.max(plotBorderWidth, clipOffset[1]) / 2 - clipX),
                    height: Math.max(0, Math.floor(chart.plotSizeY - Math.max(plotBorderWidth, clipOffset[2]) / 2 - clipY))
                };

                if (!skipAxes) {
                    each(chart.axes, function(axis) {
                        axis.setAxisSize();
                        axis.setAxisTranslation();
                    });
                }
            },

            /**
             * Initial margins before auto size margins are applied
             */
            resetMargins: function() {
                var chart = this,
                    chartOptions = chart.options.chart;

                // Create margin and spacing array
                each(['margin', 'spacing'], function splashArrays(target) {
                    var value = chartOptions[target],
                        values = isObject(value) ? value : [value, value, value, value];

                    each(['Top', 'Right', 'Bottom', 'Left'], function(sideName, side) {
                        chart[target][side] = pick(chartOptions[target + sideName], values[side]);
                    });
                });

                // Set margin names like chart.plotTop, chart.plotLeft, chart.marginRight, chart.marginBottom.
                each(marginNames, function(m, side) {
                    chart[m] = pick(chart.margin[side], chart.spacing[side]);
                });
                chart.axisOffset = [0, 0, 0, 0]; // top, right, bottom, left
                chart.clipOffset = [0, 0, 0, 0];
            },

            /**
             * Draw the borders and backgrounds for chart and plot area
             */
            drawChartBox: function() {
                var chart = this,
                    optionsChart = chart.options.chart,
                    renderer = chart.renderer,
                    chartWidth = chart.chartWidth,
                    chartHeight = chart.chartHeight,
                    chartBackground = chart.chartBackground,
                    plotBackground = chart.plotBackground,
                    plotBorder = chart.plotBorder,
                    chartBorderWidth,

                    plotBGImage = chart.plotBGImage,
                    chartBackgroundColor = optionsChart.backgroundColor,
                    plotBackgroundColor = optionsChart.plotBackgroundColor,
                    plotBackgroundImage = optionsChart.plotBackgroundImage,

                    mgn,
                    bgAttr,
                    plotLeft = chart.plotLeft,
                    plotTop = chart.plotTop,
                    plotWidth = chart.plotWidth,
                    plotHeight = chart.plotHeight,
                    plotBox = chart.plotBox,
                    clipRect = chart.clipRect,
                    clipBox = chart.clipBox,
                    verb = 'animate';

                // Chart area
                if (!chartBackground) {
                    chart.chartBackground = chartBackground = renderer.rect()
                        .addClass('highcharts-background')
                        .add();
                    verb = 'attr';
                }


                // Presentational
                chartBorderWidth = optionsChart.borderWidth || 0;
                mgn = chartBorderWidth + (optionsChart.shadow ? 8 : 0);

                bgAttr = {
                    fill: chartBackgroundColor || 'none'
                };

                if (chartBorderWidth || chartBackground['stroke-width']) { // #980
                    bgAttr.stroke = optionsChart.borderColor;
                    bgAttr['stroke-width'] = chartBorderWidth;
                }
                chartBackground
                    .attr(bgAttr)
                    .shadow(optionsChart.shadow);

                chartBackground[verb]({
                    x: mgn / 2,
                    y: mgn / 2,
                    width: chartWidth - mgn - chartBorderWidth % 2,
                    height: chartHeight - mgn - chartBorderWidth % 2,
                    r: optionsChart.borderRadius
                });

                // Plot background
                verb = 'animate';
                if (!plotBackground) {
                    verb = 'attr';
                    chart.plotBackground = plotBackground = renderer.rect()
                        .addClass('highcharts-plot-background')
                        .add();
                }
                plotBackground[verb](plotBox);


                // Presentational attributes for the background
                plotBackground
                    .attr({
                        fill: plotBackgroundColor || 'none'
                    })
                    .shadow(optionsChart.plotShadow);

                // Create the background image
                if (plotBackgroundImage) {
                    if (!plotBGImage) {
                        chart.plotBGImage = renderer.image(plotBackgroundImage, plotLeft, plotTop, plotWidth, plotHeight)
                            .add();
                    } else {
                        plotBGImage.animate(plotBox);
                    }
                }


                // Plot clip
                if (!clipRect) {
                    chart.clipRect = renderer.clipRect(clipBox);
                } else {
                    clipRect.animate({
                        width: clipBox.width,
                        height: clipBox.height
                    });
                }

                // Plot area border
                verb = 'animate';
                if (!plotBorder) {
                    verb = 'attr';
                    chart.plotBorder = plotBorder = renderer.rect()
                        .addClass('highcharts-plot-border')
                        .attr({
                            zIndex: 1 // Above the grid
                        })
                        .add();
                }


                // Presentational
                plotBorder.attr({
                    stroke: optionsChart.plotBorderColor,
                    'stroke-width': optionsChart.plotBorderWidth || 0,
                    fill: 'none'
                });


                plotBorder[verb](plotBorder.crisp({
                    x: plotLeft,
                    y: plotTop,
                    width: plotWidth,
                    height: plotHeight
                }, -plotBorder.strokeWidth())); //#3282 plotBorder should be negative;

                // reset
                chart.isDirtyBox = false;
            },

            /**
             * Detect whether a certain chart property is needed based on inspecting its options
             * and series. This mainly applies to the chart.inverted property, and in extensions to
             * the chart.angular and chart.polar properties.
             */
            propFromSeries: function() {
                var chart = this,
                    optionsChart = chart.options.chart,
                    klass,
                    seriesOptions = chart.options.series,
                    i,
                    value;


                each(['inverted', 'angular', 'polar'], function(key) {

                    // The default series type's class
                    klass = seriesTypes[optionsChart.type || optionsChart.defaultSeriesType];

                    // Get the value from available chart-wide properties
                    value =
                        optionsChart[key] || // It is set in the options
                        (klass && klass.prototype[key]); // The default series class requires it

                    // 4. Check if any the chart's series require it
                    i = seriesOptions && seriesOptions.length;
                    while (!value && i--) {
                        klass = seriesTypes[seriesOptions[i].type];
                        if (klass && klass.prototype[key]) {
                            value = true;
                        }
                    }

                    // Set the chart property
                    chart[key] = value;
                });

            },

            /**
             * Link two or more series together. This is done initially from Chart.render,
             * and after Chart.addSeries and Series.remove.
             */
            linkSeries: function() {
                var chart = this,
                    chartSeries = chart.series;

                // Reset links
                each(chartSeries, function(series) {
                    series.linkedSeries.length = 0;
                });

                // Apply new links
                each(chartSeries, function(series) {
                    var linkedTo = series.options.linkedTo;
                    if (isString(linkedTo)) {
                        if (linkedTo === ':previous') {
                            linkedTo = chart.series[series.index - 1];
                        } else {
                            linkedTo = chart.get(linkedTo);
                        }
                        if (linkedTo && linkedTo.linkedParent !== series) { // #3341 avoid mutual linking
                            linkedTo.linkedSeries.push(series);
                            series.linkedParent = linkedTo;
                            series.visible = pick(series.options.visible, linkedTo.options.visible, series.visible); // #3879
                        }
                    }
                });
            },

            /**
             * Render series for the chart
             */
            renderSeries: function() {
                each(this.series, function(serie) {
                    serie.translate();
                    serie.render();
                });
            },

            /**
             * Render labels for the chart
             */
            renderLabels: function() {
                var chart = this,
                    labels = chart.options.labels;
                if (labels.items) {
                    each(labels.items, function(label) {
                        var style = extend(labels.style, label.style),
                            x = pInt(style.left) + chart.plotLeft,
                            y = pInt(style.top) + chart.plotTop + 12;

                        // delete to prevent rewriting in IE
                        delete style.left;
                        delete style.top;

                        chart.renderer.text(
                                label.html,
                                x,
                                y
                            )
                            .attr({
                                zIndex: 2
                            })
                            .css(style)
                            .add();

                    });
                }
            },

            /**
             * Render all graphics for the chart
             */
            render: function() {
                var chart = this,
                    axes = chart.axes,
                    renderer = chart.renderer,
                    options = chart.options,
                    tempWidth,
                    tempHeight,
                    redoHorizontal,
                    redoVertical;

                // Title
                chart.setTitle();


                // Legend
                chart.legend = new Legend(chart, options.legend);

                // Get stacks
                if (chart.getStacks) {
                    chart.getStacks();
                }

                // Get chart margins
                chart.getMargins(true);
                chart.setChartSize();

                // Record preliminary dimensions for later comparison
                tempWidth = chart.plotWidth;
                tempHeight = chart.plotHeight = chart.plotHeight - 21; // 21 is the most common correction for X axis labels

                // Get margins by pre-rendering axes
                each(axes, function(axis) {
                    axis.setScale();
                });
                chart.getAxisMargins();

                // If the plot area size has changed significantly, calculate tick positions again
                redoHorizontal = tempWidth / chart.plotWidth > 1.1;
                redoVertical = tempHeight / chart.plotHeight > 1.05; // Height is more sensitive

                if (redoHorizontal || redoVertical) {

                    each(axes, function(axis) {
                        if ((axis.horiz && redoHorizontal) || (!axis.horiz && redoVertical)) {
                            axis.setTickInterval(true); // update to reflect the new margins
                        }
                    });
                    chart.getMargins(); // second pass to check for new labels
                }

                // Draw the borders and backgrounds
                chart.drawChartBox();


                // Axes
                if (chart.hasCartesianSeries) {
                    each(axes, function(axis) {
                        if (axis.visible) {
                            axis.render();
                        }
                    });
                }

                // The series
                if (!chart.seriesGroup) {
                    chart.seriesGroup = renderer.g('series-group')
                        .attr({
                            zIndex: 3
                        })
                        .add();
                }
                chart.renderSeries();

                // Labels
                chart.renderLabels();

                // Credits
                chart.addCredits();

                // Handle responsiveness
                if (chart.setResponsive) {
                    chart.setResponsive();
                }

                // Set flag
                chart.hasRendered = true;

            },

            /**
             * Show chart credits based on config options
             */
            addCredits: function(credits) {
                var chart = this;

                credits = merge(true, this.options.credits, credits);
                if (credits.enabled && !this.credits) {
                    this.credits = this.renderer.text(
                            credits.text + (this.mapCredits || ''),
                            0,
                            0
                        )
                        .addClass('highcharts-credits')
                        .on('click', function() {
                            if (credits.href) {
                                win.location.href = credits.href;
                            }
                        })
                        .attr({
                            align: credits.position.align,
                            zIndex: 8
                        })

                    .css(credits.style)

                    .add()
                        .align(credits.position);

                    // Dynamically update
                    this.credits.update = function(options) {
                        chart.credits = chart.credits.destroy();
                        chart.addCredits(options);
                    };
                }
            },

            /**
             * Clean up memory usage
             */
            destroy: function() {
                var chart = this,
                    axes = chart.axes,
                    series = chart.series,
                    container = chart.container,
                    i,
                    parentNode = container && container.parentNode;

                // fire the chart.destoy event
                fireEvent(chart, 'destroy');

                // Delete the chart from charts lookup array
                charts[chart.index] = undefined;
                H.chartCount--;
                chart.renderTo.removeAttribute('data-highcharts-chart');

                // remove events
                removeEvent(chart);

                // ==== Destroy collections:
                // Destroy axes
                i = axes.length;
                while (i--) {
                    axes[i] = axes[i].destroy();
                }

                // Destroy scroller & scroller series before destroying base series
                if (this.scroller && this.scroller.destroy) {
                    this.scroller.destroy();
                }

                // Destroy each series
                i = series.length;
                while (i--) {
                    series[i] = series[i].destroy();
                }

                // ==== Destroy chart properties:
                each(['title', 'subtitle', 'chartBackground', 'plotBackground', 'plotBGImage',
                    'plotBorder', 'seriesGroup', 'clipRect', 'credits', 'pointer',
                    'rangeSelector', 'legend', 'resetZoomButton', 'tooltip', 'renderer'
                ], function(name) {
                    var prop = chart[name];

                    if (prop && prop.destroy) {
                        chart[name] = prop.destroy();
                    }
                });

                // remove container and all SVG
                if (container) { // can break in IE when destroyed before finished loading
                    container.innerHTML = '';
                    removeEvent(container);
                    if (parentNode) {
                        discardElement(container);
                    }

                }

                // clean it all up
                for (i in chart) {
                    delete chart[i];
                }

            },


            /**
             * VML namespaces can't be added until after complete. Listening
             * for Perini's doScroll hack is not enough.
             */
            isReadyToRender: function() {
                var chart = this;

                // Note: win == win.top is required
                if ((!svg && (win == win.top && doc.readyState !== 'complete'))) { // eslint-disable-line eqeqeq
                    doc.attachEvent('onreadystatechange', function() {
                        doc.detachEvent('onreadystatechange', chart.firstRender);
                        if (doc.readyState === 'complete') {
                            chart.firstRender();
                        }
                    });
                    return false;
                }
                return true;
            },

            /**
             * Prepare for first rendering after all data are loaded
             */
            firstRender: function() {
                var chart = this,
                    options = chart.options;

                // Check whether the chart is ready to render
                if (!chart.isReadyToRender()) {
                    return;
                }

                // Create the container
                chart.getContainer();

                // Run an early event after the container and renderer are established
                fireEvent(chart, 'init');


                chart.resetMargins();
                chart.setChartSize();

                // Set the common chart properties (mainly invert) from the given series
                chart.propFromSeries();

                // get axes
                chart.getAxes();

                // Initialize the series
                each(options.series || [], function(serieOptions) {
                    chart.initSeries(serieOptions);
                });

                chart.linkSeries();

                // Run an event after axes and series are initialized, but before render. At this stage,
                // the series data is indexed and cached in the xData and yData arrays, so we can access
                // those before rendering. Used in Highstock.
                fireEvent(chart, 'beforeRender');

                // depends on inverted and on margins being set
                if (Pointer) {
                    chart.pointer = new Pointer(chart, options);
                }

                chart.render();

                // add canvas
                chart.renderer.draw();

                // Fire the load event if there are no external images
                if (!chart.renderer.imgCount && chart.onload) {
                    chart.onload();
                }

                // If the chart was rendered outside the top container, put it back in (#3679)
                chart.cloneRenderTo(true);

            },

            /** 
             * On chart load
             */
            onload: function() {

                // Run callbacks
                each([this.callback].concat(this.callbacks), function(fn) {
                    if (fn && this.index !== undefined) { // Chart destroyed in its own callback (#3600)
                        fn.apply(this, [this]);
                    }
                }, this);

                fireEvent(this, 'load');

                // Set up auto resize
                if (this.options.chart.reflow !== false) {
                    this.initReflow();
                }

                // Don't run again
                this.onload = null;
            }

        }; // end Chart

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var Point,

            each = H.each,
            extend = H.extend,
            erase = H.erase,
            fireEvent = H.fireEvent,
            format = H.format,
            isArray = H.isArray,
            isNumber = H.isNumber,
            pick = H.pick,
            removeEvent = H.removeEvent;

        /**
         * The Point object. The point objects are generated from the series.data 
         * configuration objects or raw numbers. They can be accessed from the
         * Series.points array.
         * @constructor Point
         */
        Point = H.Point = function() {};
        Point.prototype = {

            /**
             * Initialize the point. Called internally based on the series.data option.
             * @function #init
             * @memberOf Point
             * @param {Object} series The series object containing this point.
             * @param {Object} options The data in either number, array or object
             *        format.
             * @param {Number} x Optionally, the X value of the.
             * @returns {Object} The Point instance.
             */
            init: function(series, options, x) {

                var point = this,
                    colors,
                    colorCount = series.chart.options.chart.colorCount,
                    colorIndex;

                point.series = series;

                point.color = series.color; // #3445

                point.applyOptions(options, x);

                if (series.options.colorByPoint) {

                    colors = series.options.colors || series.chart.options.colors;
                    point.color = point.color || colors[series.colorCounter];
                    colorCount = colors.length;

                    colorIndex = series.colorCounter;
                    series.colorCounter++;
                    // loop back to zero
                    if (series.colorCounter === colorCount) {
                        series.colorCounter = 0;
                    }
                } else {
                    colorIndex = series.colorIndex;
                }
                point.colorIndex = pick(point.colorIndex, colorIndex);

                series.chart.pointCount++;
                return point;
            },
            /**
             * Apply the options containing the x and y data and possible some extra
             * properties. Called on point init or from point.update.
             *
             * @function #applyOptions
             * @memberOf Point
             * @param {Object} options The point options as defined in series.data.
             * @param {Number} x Optionally, the X value.
             * @returns {Object} The Point instance.
             */
            applyOptions: function(options, x) {
                var point = this,
                    series = point.series,
                    pointValKey = series.options.pointValKey || series.pointValKey;

                options = Point.prototype.optionsToObject.call(this, options);

                // copy options directly to point
                extend(point, options);
                point.options = point.options ? extend(point.options, options) : options;

                // Since options are copied into the Point instance, some accidental options must be shielded (#5681)
                if (options.group) {
                    delete point.group;
                }

                // For higher dimension series types. For instance, for ranges, point.y is mapped to point.low.
                if (pointValKey) {
                    point.y = point[pointValKey];
                }
                point.isNull = pick(
                    point.isValid && !point.isValid(),
                    point.x === null || !isNumber(point.y, true)
                ); // #3571, check for NaN

                // The point is initially selected by options (#5777)
                if (point.selected) {
                    point.state = 'select';
                }

                // If no x is set by now, get auto incremented value. All points must have an
                // x value, however the y value can be null to create a gap in the series
                if ('name' in point && x === undefined && series.xAxis && series.xAxis.hasNames) {
                    point.x = series.xAxis.nameToX(point);
                }
                if (point.x === undefined && series) {
                    if (x === undefined) {
                        point.x = series.autoIncrement(point);
                    } else {
                        point.x = x;
                    }
                }

                return point;
            },

            /**
             * Transform number or array configs into objects
             */
            optionsToObject: function(options) {
                var ret = {},
                    series = this.series,
                    keys = series.options.keys,
                    pointArrayMap = keys || series.pointArrayMap || ['y'],
                    valueCount = pointArrayMap.length,
                    firstItemType,
                    i = 0,
                    j = 0;

                if (isNumber(options) || options === null) {
                    ret[pointArrayMap[0]] = options;

                } else if (isArray(options)) {
                    // with leading x value
                    if (!keys && options.length > valueCount) {
                        firstItemType = typeof options[0];
                        if (firstItemType === 'string') {
                            ret.name = options[0];
                        } else if (firstItemType === 'number') {
                            ret.x = options[0];
                        }
                        i++;
                    }
                    while (j < valueCount) {
                        if (!keys || options[i] !== undefined) { // Skip undefined positions for keys
                            ret[pointArrayMap[j]] = options[i];
                        }
                        i++;
                        j++;
                    }
                } else if (typeof options === 'object') {
                    ret = options;

                    // This is the fastest way to detect if there are individual point dataLabels that need
                    // to be considered in drawDataLabels. These can only occur in object configs.
                    if (options.dataLabels) {
                        series._hasPointLabels = true;
                    }

                    // Same approach as above for markers
                    if (options.marker) {
                        series._hasPointMarkers = true;
                    }
                }
                return ret;
            },

            /**
             * Get the CSS class names for individual points
             * @returns {String} The class name
             */
            getClassName: function() {
                return 'highcharts-point' +
                    (this.selected ? ' highcharts-point-select' : '') +
                    (this.negative ? ' highcharts-negative' : '') +
                    (this.isNull ? ' highcharts-null-point' : '') +
                    (this.colorIndex !== undefined ? ' highcharts-color-' + this.colorIndex : '') +
                    (this.options.className ? ' ' + this.options.className : '');
            },

            /**
             * Return the zone that the point belongs to
             */
            getZone: function() {
                var series = this.series,
                    zones = series.zones,
                    zoneAxis = series.zoneAxis || 'y',
                    i = 0,
                    zone;

                zone = zones[i];
                while (this[zoneAxis] >= zone.value) {
                    zone = zones[++i];
                }

                if (zone && zone.color && !this.options.color) {
                    this.color = zone.color;
                }

                return zone;
            },

            /**
             * Destroy a point to clear memory. Its reference still stays in series.data.
             */
            destroy: function() {
                var point = this,
                    series = point.series,
                    chart = series.chart,
                    hoverPoints = chart.hoverPoints,
                    prop;

                chart.pointCount--;

                if (hoverPoints) {
                    point.setState();
                    erase(hoverPoints, point);
                    if (!hoverPoints.length) {
                        chart.hoverPoints = null;
                    }

                }
                if (point === chart.hoverPoint) {
                    point.onMouseOut();
                }

                // remove all events
                if (point.graphic || point.dataLabel) { // removeEvent and destroyElements are performance expensive
                    removeEvent(point);
                    point.destroyElements();
                }

                if (point.legendItem) { // pies have legend items
                    chart.legend.destroyItem(point);
                }

                for (prop in point) {
                    point[prop] = null;
                }


            },

            /**
             * Destroy SVG elements associated with the point
             */
            destroyElements: function() {
                var point = this,
                    props = ['graphic', 'dataLabel', 'dataLabelUpper', 'connector', 'shadowGroup'],
                    prop,
                    i = 6;
                while (i--) {
                    prop = props[i];
                    if (point[prop]) {
                        point[prop] = point[prop].destroy();
                    }
                }
            },

            /**
             * Return the configuration hash needed for the data label and tooltip formatters
             */
            getLabelConfig: function() {
                return {
                    x: this.category,
                    y: this.y,
                    color: this.color,
                    key: this.name || this.category,
                    series: this.series,
                    point: this,
                    percentage: this.percentage,
                    total: this.total || this.stackTotal
                };
            },

            /**
             * Extendable method for formatting each point's tooltip line
             *
             * @return {String} A string to be concatenated in to the common tooltip text
             */
            tooltipFormatter: function(pointFormat) {

                // Insert options for valueDecimals, valuePrefix, and valueSuffix
                var series = this.series,
                    seriesTooltipOptions = series.tooltipOptions,
                    valueDecimals = pick(seriesTooltipOptions.valueDecimals, ''),
                    valuePrefix = seriesTooltipOptions.valuePrefix || '',
                    valueSuffix = seriesTooltipOptions.valueSuffix || '';

                // Loop over the point array map and replace unformatted values with sprintf formatting markup
                each(series.pointArrayMap || ['y'], function(key) {
                    key = '{point.' + key; // without the closing bracket
                    if (valuePrefix || valueSuffix) {
                        pointFormat = pointFormat.replace(key + '}', valuePrefix + key + '}' + valueSuffix);
                    }
                    pointFormat = pointFormat.replace(key + '}', key + ':,.' + valueDecimals + 'f}');
                });

                return format(pointFormat, {
                    point: this,
                    series: this.series
                });
            },

            /**
             * Fire an event on the Point object.
             * @param {String} eventType
             * @param {Object} eventArgs Additional event arguments
             * @param {Function} defaultFunction Default event handler
             */
            firePointEvent: function(eventType, eventArgs, defaultFunction) {
                var point = this,
                    series = this.series,
                    seriesOptions = series.options;

                // load event handlers on demand to save time on mouseover/out
                if (seriesOptions.point.events[eventType] || (point.options && point.options.events && point.options.events[eventType])) {
                    this.importEvents();
                }

                // add default handler if in selection mode
                if (eventType === 'click' && seriesOptions.allowPointSelect) {
                    defaultFunction = function(event) {
                        // Control key is for Windows, meta (= Cmd key) for Mac, Shift for Opera
                        if (point.select) { // Could be destroyed by prior event handlers (#2911)
                            point.select(null, event.ctrlKey || event.metaKey || event.shiftKey);
                        }
                    };
                }

                fireEvent(this, eventType, eventArgs, defaultFunction);
            },
            visible: true
        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var addEvent = H.addEvent,
            animObject = H.animObject,
            arrayMax = H.arrayMax,
            arrayMin = H.arrayMin,
            correctFloat = H.correctFloat,
            Date = H.Date,
            defaultOptions = H.defaultOptions,
            defaultPlotOptions = H.defaultPlotOptions,
            defined = H.defined,
            each = H.each,
            erase = H.erase,
            error = H.error,
            extend = H.extend,
            fireEvent = H.fireEvent,
            grep = H.grep,
            isArray = H.isArray,
            isNumber = H.isNumber,
            isString = H.isString,
            LegendSymbolMixin = H.LegendSymbolMixin, // @todo add as a requirement
            merge = H.merge,
            pick = H.pick,
            Point = H.Point, // @todo  add as a requirement
            removeEvent = H.removeEvent,
            splat = H.splat,
            stableSort = H.stableSort,
            SVGElement = H.SVGElement,
            syncTimeout = H.syncTimeout,
            win = H.win;

        /**
         * The base function which all other series types inherit from. The data in the series is stored
         * in various arrays.
         *
         * - First, series.options.data contains all the original config options for
         * each point whether added by options or methods like series.addPoint.
         * - Next, series.data contains those values converted to points, but in case the series data length
         * exceeds the cropThreshold, or if the data is grouped, series.data doesn't contain all the points. It
         * only contains the points that have been created on demand.
         * - Then there's series.points that contains all currently visible point objects. In case of cropping,
         * the cropped-away points are not part of this array. The series.points array starts at series.cropStart
         * compared to series.data and series.options.data. If however the series data is grouped, these can't
         * be correlated one to one.
         * - series.xData and series.processedXData contain clean x values, equivalent to series.data and series.points.
         * - series.yData and series.processedYData contain clean y values, equivalent to series.data and series.points.
         *
         * @constructor Series
         * @param {Object} chart - The chart instance.
         * @param {Object} options - The series options.
         */
        H.Series = H.seriesType('line', null, { // base series options

            //cursor: 'default',
            //dashStyle: null,
            //linecap: 'round',
            lineWidth: 2,
            //shadow: false,

            allowPointSelect: false,
            showCheckbox: false,
            animation: {
                duration: 1000
            },
            //clip: true,
            //connectNulls: false,
            //enableMouseTracking: true,
            events: {},
            //legendIndex: 0,
            // stacking: null,
            marker: {

                lineWidth: 0,
                lineColor: '#ffffff',
                //fillColor: null,

                //enabled: true,
                //symbol: null,
                radius: 4,
                states: { // states for a single point
                    hover: {
                        animation: {
                            duration: 50
                        },
                        enabled: true,
                        radiusPlus: 2,

                        lineWidthPlus: 1

                    },

                    select: {
                        fillColor: '#cccccc',
                        lineColor: '#000000',
                        lineWidth: 2
                    }

                }
            },
            point: {
                events: {}
            },
            dataLabels: {
                align: 'center',
                // defer: true,
                // enabled: false,
                formatter: function() {
                    return this.y === null ? '' : H.numberFormat(this.y, -1);
                },

                style: {
                    fontSize: '11px',
                    fontWeight: 'bold',
                    color: 'contrast',
                    textOutline: '1px contrast'
                },
                // backgroundColor: undefined,
                // borderColor: undefined,
                // borderWidth: undefined,
                // shadow: false

                verticalAlign: 'bottom', // above singular point
                x: 0,
                y: 0,
                // borderRadius: undefined,
                padding: 5
            },
            cropThreshold: 300, // draw points outside the plot area when the number of points is less than this
            pointRange: 0,
            //pointStart: 0,
            //pointInterval: 1,
            //showInLegend: null, // auto: true for standalone series, false for linked series
            softThreshold: true,
            states: { // states for the entire series
                hover: {
                    //enabled: false,
                    lineWidthPlus: 1,
                    marker: {
                        // lineWidth: base + 1,
                        // radius: base + 1
                    },
                    halo: {
                        size: 10,

                        opacity: 0.25

                    }
                },
                select: {
                    marker: {}
                }
            },
            stickyTracking: true,
            //tooltip: {
            //pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}</b>'
            //valueDecimals: null,
            //xDateFormat: '%A, %b %e, %Y',
            //valuePrefix: '',
            //ySuffix: ''
            //}
            turboThreshold: 1000
                // zIndex: null


        }, /** @lends Series.prototype */ {
            isCartesian: true,
            pointClass: Point,
            sorted: true, // requires the data to be sorted
            requireSorting: true,
            directTouch: false,
            axisTypes: ['xAxis', 'yAxis'],
            colorCounter: 0,
            parallelArrays: ['x', 'y'], // each point's x and y values are stored in this.xData and this.yData
            coll: 'series',
            init: function(chart, options) {
                var series = this,
                    eventType,
                    events,
                    chartSeries = chart.series,
                    lastSeries,
                    sortByIndex = function(a, b) {
                        return pick(a.options.index, a._i) - pick(b.options.index, b._i);
                    };

                series.chart = chart;
                series.options = options = series.setOptions(options); // merge with plotOptions
                series.linkedSeries = [];

                // bind the axes
                series.bindAxes();

                // set some variables
                extend(series, {
                    name: options.name,
                    state: '',
                    visible: options.visible !== false, // true by default
                    selected: options.selected === true // false by default
                });

                // register event listeners
                events = options.events;
                for (eventType in events) {
                    addEvent(series, eventType, events[eventType]);
                }
                if (
                    (events && events.click) ||
                    (options.point && options.point.events && options.point.events.click) ||
                    options.allowPointSelect
                ) {
                    chart.runTrackerClick = true;
                }

                series.getColor();
                series.getSymbol();

                // Set the data
                each(series.parallelArrays, function(key) {
                    series[key + 'Data'] = [];
                });
                series.setData(options.data, false);

                // Mark cartesian
                if (series.isCartesian) {
                    chart.hasCartesianSeries = true;
                }

                // Get the index and register the series in the chart. The index is one
                // more than the current latest series index (#5960).
                if (chartSeries.length) {
                    lastSeries = chartSeries[chartSeries.length - 1];
                }
                series._i = pick(lastSeries && lastSeries._i, -1) + 1;
                chartSeries.push(series);

                // Sort series according to index option (#248, #1123, #2456)
                stableSort(chartSeries, sortByIndex);
                if (this.yAxis) {
                    stableSort(this.yAxis.series, sortByIndex);
                }

                each(chartSeries, function(series, i) {
                    series.index = i;
                    series.name = series.name || 'Series ' + (i + 1);
                });

            },

            /**
             * Set the xAxis and yAxis properties of cartesian series, and register the
             * series in the `axis.series` array.
             *
             * @function #bindAxes
             * @memberOf Series
             * @returns {void}
             */
            bindAxes: function() {
                var series = this,
                    seriesOptions = series.options,
                    chart = series.chart,
                    axisOptions;

                each(series.axisTypes || [], function(AXIS) { // repeat for xAxis and yAxis

                    each(chart[AXIS], function(axis) { // loop through the chart's axis objects
                        axisOptions = axis.options;

                        // apply if the series xAxis or yAxis option mathches the number of the
                        // axis, or if undefined, use the first axis
                        if ((seriesOptions[AXIS] === axisOptions.index) ||
                            (seriesOptions[AXIS] !== undefined && seriesOptions[AXIS] === axisOptions.id) ||
                            (seriesOptions[AXIS] === undefined && axisOptions.index === 0)) {

                            // register this series in the axis.series lookup
                            axis.series.push(series);

                            // set this series.xAxis or series.yAxis reference
                            series[AXIS] = axis;

                            // mark dirty for redraw
                            axis.isDirty = true;
                        }
                    });

                    // The series needs an X and an Y axis
                    if (!series[AXIS] && series.optionalAxis !== AXIS) {
                        error(18, true);
                    }

                });
            },

            /**
             * For simple series types like line and column, the data values are held in arrays like
             * xData and yData for quick lookup to find extremes and more. For multidimensional series
             * like bubble and map, this can be extended with arrays like zData and valueData by
             * adding to the series.parallelArrays array.
             */
            updateParallelArrays: function(point, i) {
                var series = point.series,
                    args = arguments,
                    fn = isNumber(i) ?
                    // Insert the value in the given position
                    function(key) {
                        var val = key === 'y' && series.toYData ? series.toYData(point) : point[key];
                        series[key + 'Data'][i] = val;
                    } :
                    // Apply the method specified in i with the following arguments as arguments
                    function(key) {
                        Array.prototype[i].apply(series[key + 'Data'], Array.prototype.slice.call(args, 2));
                    };

                each(series.parallelArrays, fn);
            },

            /**
             * Return an auto incremented x value based on the pointStart and pointInterval options.
             * This is only used if an x value is not given for the point that calls autoIncrement.
             */
            autoIncrement: function() {

                var options = this.options,
                    xIncrement = this.xIncrement,
                    date,
                    pointInterval,
                    pointIntervalUnit = options.pointIntervalUnit;

                xIncrement = pick(xIncrement, options.pointStart, 0);

                this.pointInterval = pointInterval = pick(this.pointInterval, options.pointInterval, 1);

                // Added code for pointInterval strings
                if (pointIntervalUnit) {
                    date = new Date(xIncrement);

                    if (pointIntervalUnit === 'day') {
                        date = +date[Date.hcSetDate](date[Date.hcGetDate]() + pointInterval);
                    } else if (pointIntervalUnit === 'month') {
                        date = +date[Date.hcSetMonth](date[Date.hcGetMonth]() + pointInterval);
                    } else if (pointIntervalUnit === 'year') {
                        date = +date[Date.hcSetFullYear](date[Date.hcGetFullYear]() + pointInterval);
                    }
                    pointInterval = date - xIncrement;

                }

                this.xIncrement = xIncrement + pointInterval;
                return xIncrement;
            },

            /**
             * Set the series options by merging from the options tree
             * @param {Object} itemOptions
             */
            setOptions: function(itemOptions) {
                var chart = this.chart,
                    chartOptions = chart.options,
                    plotOptions = chartOptions.plotOptions,
                    userOptions = chart.userOptions || {},
                    userPlotOptions = userOptions.plotOptions || {},
                    typeOptions = plotOptions[this.type],
                    options,
                    zones;

                this.userOptions = itemOptions;

                // General series options take precedence over type options because otherwise, default
                // type options like column.animation would be overwritten by the general option.
                // But issues have been raised here (#3881), and the solution may be to distinguish
                // between default option and userOptions like in the tooltip below.
                options = merge(
                    typeOptions,
                    plotOptions.series,
                    itemOptions
                );

                // The tooltip options are merged between global and series specific options
                this.tooltipOptions = merge(
                    defaultOptions.tooltip,
                    defaultOptions.plotOptions[this.type].tooltip,
                    userOptions.tooltip,
                    userPlotOptions.series && userPlotOptions.series.tooltip,
                    userPlotOptions[this.type] && userPlotOptions[this.type].tooltip,
                    itemOptions.tooltip
                );

                // Delete marker object if not allowed (#1125)
                if (typeOptions.marker === null) {
                    delete options.marker;
                }

                // Handle color zones
                this.zoneAxis = options.zoneAxis;
                zones = this.zones = (options.zones || []).slice();
                if ((options.negativeColor || options.negativeFillColor) && !options.zones) {
                    zones.push({
                        value: options[this.zoneAxis + 'Threshold'] || options.threshold || 0,
                        className: 'highcharts-negative',

                        color: options.negativeColor,
                        fillColor: options.negativeFillColor

                    });
                }
                if (zones.length) { // Push one extra zone for the rest
                    if (defined(zones[zones.length - 1].value)) {
                        zones.push({

                            color: this.color,
                            fillColor: this.fillColor

                        });
                    }
                }
                return options;
            },

            getCyclic: function(prop, value, defaults) {
                var i,
                    userOptions = this.userOptions,
                    indexName = prop + 'Index',
                    counterName = prop + 'Counter',
                    len = defaults ? defaults.length : pick(this.chart.options.chart[prop + 'Count'], this.chart[prop + 'Count']),
                    setting;

                if (!value) {
                    // Pick up either the colorIndex option, or the _colorIndex after Series.update()
                    setting = pick(userOptions[indexName], userOptions['_' + indexName]);
                    if (defined(setting)) { // after Series.update()
                        i = setting;
                    } else {
                        userOptions['_' + indexName] = i = this.chart[counterName] % len;
                        this.chart[counterName] += 1;
                    }
                    if (defaults) {
                        value = defaults[i];
                    }
                }
                // Set the colorIndex
                if (i !== undefined) {
                    this[indexName] = i;
                }
                this[prop] = value;
            },

            /**
             * Get the series' color
             */

            getColor: function() {
                if (this.options.colorByPoint) {
                    this.options.color = null; // #4359, selected slice got series.color even when colorByPoint was set.
                } else {
                    this.getCyclic('color', this.options.color || defaultPlotOptions[this.type].color, this.chart.options.colors);
                }
            },

            /**
             * Get the series' symbol
             */
            getSymbol: function() {
                var seriesMarkerOption = this.options.marker;

                this.getCyclic('symbol', seriesMarkerOption.symbol, this.chart.options.symbols);
            },

            drawLegendSymbol: LegendSymbolMixin.drawLineMarker,

            /**
             * Replace the series data with a new set of data
             * @param {Object} data
             * @param {Object} redraw
             */
            setData: function(data, redraw, animation, updatePoints) {
                var series = this,
                    oldData = series.points,
                    oldDataLength = (oldData && oldData.length) || 0,
                    dataLength,
                    options = series.options,
                    chart = series.chart,
                    firstPoint = null,
                    xAxis = series.xAxis,
                    i,
                    turboThreshold = options.turboThreshold,
                    pt,
                    xData = this.xData,
                    yData = this.yData,
                    pointArrayMap = series.pointArrayMap,
                    valueCount = pointArrayMap && pointArrayMap.length;

                data = data || [];
                dataLength = data.length;
                redraw = pick(redraw, true);

                // If the point count is the same as is was, just run Point.update which is
                // cheaper, allows animation, and keeps references to points.
                if (updatePoints !== false && dataLength && oldDataLength === dataLength && !series.cropped && !series.hasGroupedData && series.visible) {
                    each(data, function(point, i) {
                        // .update doesn't exist on a linked, hidden series (#3709)
                        if (oldData[i].update && point !== options.data[i]) {
                            oldData[i].update(point, false, null, false);
                        }
                    });

                } else {

                    // Reset properties
                    series.xIncrement = null;

                    series.colorCounter = 0; // for series with colorByPoint (#1547)

                    // Update parallel arrays
                    each(this.parallelArrays, function(key) {
                        series[key + 'Data'].length = 0;
                    });

                    // In turbo mode, only one- or twodimensional arrays of numbers are allowed. The
                    // first value is tested, and we assume that all the rest are defined the same
                    // way. Although the 'for' loops are similar, they are repeated inside each
                    // if-else conditional for max performance.
                    if (turboThreshold && dataLength > turboThreshold) {

                        // find the first non-null point
                        i = 0;
                        while (firstPoint === null && i < dataLength) {
                            firstPoint = data[i];
                            i++;
                        }


                        if (isNumber(firstPoint)) { // assume all points are numbers
                            for (i = 0; i < dataLength; i++) {
                                xData[i] = this.autoIncrement();
                                yData[i] = data[i];
                            }
                        } else if (isArray(firstPoint)) { // assume all points are arrays
                            if (valueCount) { // [x, low, high] or [x, o, h, l, c]
                                for (i = 0; i < dataLength; i++) {
                                    pt = data[i];
                                    xData[i] = pt[0];
                                    yData[i] = pt.slice(1, valueCount + 1);
                                }
                            } else { // [x, y]
                                for (i = 0; i < dataLength; i++) {
                                    pt = data[i];
                                    xData[i] = pt[0];
                                    yData[i] = pt[1];
                                }
                            }
                        } else {
                            error(12); // Highcharts expects configs to be numbers or arrays in turbo mode
                        }
                    } else {
                        for (i = 0; i < dataLength; i++) {
                            if (data[i] !== undefined) { // stray commas in oldIE
                                pt = {
                                    series: series
                                };
                                series.pointClass.prototype.applyOptions.apply(pt, [data[i]]);
                                series.updateParallelArrays(pt, i);
                            }
                        }
                    }

                    // Forgetting to cast strings to numbers is a common caveat when handling CSV or JSON
                    if (isString(yData[0])) {
                        error(14, true);
                    }

                    series.data = [];
                    series.options.data = series.userOptions.data = data;

                    // destroy old points
                    i = oldDataLength;
                    while (i--) {
                        if (oldData[i] && oldData[i].destroy) {
                            oldData[i].destroy();
                        }
                    }

                    // reset minRange (#878)
                    if (xAxis) {
                        xAxis.minRange = xAxis.userMinRange;
                    }

                    // redraw
                    series.isDirty = chart.isDirtyBox = true;
                    series.isDirtyData = !!oldData;
                    animation = false;
                }

                // Typically for pie series, points need to be processed and generated
                // prior to rendering the legend
                if (options.legendType === 'point') {
                    this.processData();
                    this.generatePoints();
                }

                if (redraw) {
                    chart.redraw(animation);
                }
            },

            /**
             * Process the data by cropping away unused data points if the series is longer
             * than the crop threshold. This saves computing time for lage series.
             */
            processData: function(force) {
                var series = this,
                    processedXData = series.xData, // copied during slice operation below
                    processedYData = series.yData,
                    dataLength = processedXData.length,
                    croppedData,
                    cropStart = 0,
                    cropped,
                    distance,
                    closestPointRange,
                    xAxis = series.xAxis,
                    i, // loop variable
                    options = series.options,
                    cropThreshold = options.cropThreshold,
                    getExtremesFromAll = series.getExtremesFromAll || options.getExtremesFromAll, // #4599
                    isCartesian = series.isCartesian,
                    xExtremes,
                    val2lin = xAxis && xAxis.val2lin,
                    isLog = xAxis && xAxis.isLog,
                    min,
                    max;

                // If the series data or axes haven't changed, don't go through this. Return false to pass
                // the message on to override methods like in data grouping.
                if (isCartesian && !series.isDirty && !xAxis.isDirty && !series.yAxis.isDirty && !force) {
                    return false;
                }

                if (xAxis) {
                    xExtremes = xAxis.getExtremes(); // corrected for log axis (#3053)
                    min = xExtremes.min;
                    max = xExtremes.max;
                }

                // optionally filter out points outside the plot area
                if (isCartesian && series.sorted && !getExtremesFromAll && (!cropThreshold || dataLength > cropThreshold || series.forceCrop)) {

                    // it's outside current extremes
                    if (processedXData[dataLength - 1] < min || processedXData[0] > max) {
                        processedXData = [];
                        processedYData = [];

                        // only crop if it's actually spilling out
                    } else if (processedXData[0] < min || processedXData[dataLength - 1] > max) {
                        croppedData = this.cropData(series.xData, series.yData, min, max);
                        processedXData = croppedData.xData;
                        processedYData = croppedData.yData;
                        cropStart = croppedData.start;
                        cropped = true;
                    }
                }


                // Find the closest distance between processed points
                i = processedXData.length || 1;
                while (--i) {
                    distance = isLog ?
                        val2lin(processedXData[i]) - val2lin(processedXData[i - 1]) :
                        processedXData[i] - processedXData[i - 1];

                    if (distance > 0 && (closestPointRange === undefined || distance < closestPointRange)) {
                        closestPointRange = distance;

                        // Unsorted data is not supported by the line tooltip, as well as data grouping and
                        // navigation in Stock charts (#725) and width calculation of columns (#1900)
                    } else if (distance < 0 && series.requireSorting) {
                        error(15);
                    }
                }

                // Record the properties
                series.cropped = cropped; // undefined or true
                series.cropStart = cropStart;
                series.processedXData = processedXData;
                series.processedYData = processedYData;

                series.closestPointRange = closestPointRange;

            },

            /**
             * Iterate over xData and crop values between min and max. Returns object containing crop start/end
             * cropped xData with corresponding part of yData, dataMin and dataMax within the cropped range
             */
            cropData: function(xData, yData, min, max) {
                var dataLength = xData.length,
                    cropStart = 0,
                    cropEnd = dataLength,
                    cropShoulder = pick(this.cropShoulder, 1), // line-type series need one point outside
                    i,
                    j;

                // iterate up to find slice start
                for (i = 0; i < dataLength; i++) {
                    if (xData[i] >= min) {
                        cropStart = Math.max(0, i - cropShoulder);
                        break;
                    }
                }

                // proceed to find slice end
                for (j = i; j < dataLength; j++) {
                    if (xData[j] > max) {
                        cropEnd = j + cropShoulder;
                        break;
                    }
                }

                return {
                    xData: xData.slice(cropStart, cropEnd),
                    yData: yData.slice(cropStart, cropEnd),
                    start: cropStart,
                    end: cropEnd
                };
            },


            /**
             * Generate the data point after the data has been processed by cropping away
             * unused points and optionally grouped in Highcharts Stock.
             */
            generatePoints: function() {
                var series = this,
                    options = series.options,
                    dataOptions = options.data,
                    data = series.data,
                    dataLength,
                    processedXData = series.processedXData,
                    processedYData = series.processedYData,
                    PointClass = series.pointClass,
                    processedDataLength = processedXData.length,
                    cropStart = series.cropStart || 0,
                    cursor,
                    hasGroupedData = series.hasGroupedData,
                    point,
                    points = [],
                    i;

                if (!data && !hasGroupedData) {
                    var arr = [];
                    arr.length = dataOptions.length;
                    data = series.data = arr;
                }

                for (i = 0; i < processedDataLength; i++) {
                    cursor = cropStart + i;
                    if (!hasGroupedData) {
                        point = data[cursor];
                        if (!point && dataOptions[cursor] !== undefined) { // #970
                            data[cursor] = point = (new PointClass()).init(series, dataOptions[cursor], processedXData[i]);
                        }
                    } else {
                        // splat the y data in case of ohlc data array
                        point = (new PointClass()).init(series, [processedXData[i]].concat(splat(processedYData[i])));
                        point.dataGroup = series.groupMap[i];
                    }
                    point.index = cursor; // For faster access in Point.update
                    points[i] = point;
                }

                // Hide cropped-away points - this only runs when the number of points is above cropThreshold, or when
                // swithching view from non-grouped data to grouped data (#637)
                if (data && (processedDataLength !== (dataLength = data.length) || hasGroupedData)) {
                    for (i = 0; i < dataLength; i++) {
                        if (i === cropStart && !hasGroupedData) { // when has grouped data, clear all points
                            i += processedDataLength;
                        }
                        if (data[i]) {
                            data[i].destroyElements();
                            data[i].plotX = undefined; // #1003
                        }
                    }
                }

                series.data = data;
                series.points = points;
            },

            /**
             * Calculate Y extremes for visible data
             */
            getExtremes: function(yData) {
                var xAxis = this.xAxis,
                    yAxis = this.yAxis,
                    xData = this.processedXData,
                    yDataLength,
                    activeYData = [],
                    activeCounter = 0,
                    xExtremes = xAxis.getExtremes(), // #2117, need to compensate for log X axis
                    xMin = xExtremes.min,
                    xMax = xExtremes.max,
                    validValue,
                    withinRange,
                    x,
                    y,
                    i,
                    j;

                yData = yData || this.stackedYData || this.processedYData || [];
                yDataLength = yData.length;

                for (i = 0; i < yDataLength; i++) {

                    x = xData[i];
                    y = yData[i];

                    // For points within the visible range, including the first point outside the
                    // visible range, consider y extremes
                    validValue = (isNumber(y, true) || isArray(y)) && (!yAxis.isLog || (y.length || y > 0));
                    withinRange = this.getExtremesFromAll || this.options.getExtremesFromAll || this.cropped ||
                        ((xData[i + 1] || x) >= xMin && (xData[i - 1] || x) <= xMax);

                    if (validValue && withinRange) {

                        j = y.length;
                        if (j) { // array, like ohlc or range data
                            while (j--) {
                                if (y[j] !== null) {
                                    activeYData[activeCounter++] = y[j];
                                }
                            }
                        } else {
                            activeYData[activeCounter++] = y;
                        }
                    }
                }
                this.dataMin = arrayMin(activeYData);
                this.dataMax = arrayMax(activeYData);
            },

            /**
             * Translate data points from raw data values to chart specific positioning
             * data needed later in drawPoints, drawGraph and drawTracker.
             *
             * @function #translate
             * @memberOf Series
             * @returns {void}
             */
            translate: function() {
                if (!this.processedXData) { // hidden series
                    this.processData();
                }
                this.generatePoints();
                var series = this,
                    options = series.options,
                    stacking = options.stacking,
                    xAxis = series.xAxis,
                    categories = xAxis.categories,
                    yAxis = series.yAxis,
                    points = series.points,
                    dataLength = points.length,
                    hasModifyValue = !!series.modifyValue,
                    i,
                    pointPlacement = options.pointPlacement,
                    dynamicallyPlaced = pointPlacement === 'between' || isNumber(pointPlacement),
                    threshold = options.threshold,
                    stackThreshold = options.startFromThreshold ? threshold : 0,
                    plotX,
                    plotY,
                    lastPlotX,
                    stackIndicator,
                    closestPointRangePx = Number.MAX_VALUE;

                // Point placement is relative to each series pointRange (#5889)
                if (pointPlacement === 'between') {
                    pointPlacement = 0.5;
                }
                if (isNumber(pointPlacement)) {
                    pointPlacement *= pick(options.pointRange || xAxis.pointRange);
                }

                // Translate each point
                for (i = 0; i < dataLength; i++) {
                    var point = points[i],
                        xValue = point.x,
                        yValue = point.y,
                        yBottom = point.low,
                        stack = stacking && yAxis.stacks[(series.negStacks && yValue < (stackThreshold ? 0 : threshold) ? '-' : '') + series.stackKey],
                        pointStack,
                        stackValues;

                    // Discard disallowed y values for log axes (#3434)
                    if (yAxis.isLog && yValue !== null && yValue <= 0) {
                        point.isNull = true;
                    }

                    // Get the plotX translation
                    point.plotX = plotX = correctFloat( // #5236
                        Math.min(Math.max(-1e5, xAxis.translate(
                            xValue,
                            0,
                            0,
                            0,
                            1,
                            pointPlacement,
                            this.type === 'flags'
                        )), 1e5) // #3923
                    );

                    // Calculate the bottom y value for stacked series
                    if (stacking && series.visible && !point.isNull && stack && stack[xValue]) {
                        stackIndicator = series.getStackIndicator(stackIndicator, xValue, series.index);
                        pointStack = stack[xValue];
                        stackValues = pointStack.points[stackIndicator.key];
                        yBottom = stackValues[0];
                        yValue = stackValues[1];

                        if (yBottom === stackThreshold && stackIndicator.key === stack[xValue].base) {
                            yBottom = pick(threshold, yAxis.min);
                        }
                        if (yAxis.isLog && yBottom <= 0) { // #1200, #1232
                            yBottom = null;
                        }

                        point.total = point.stackTotal = pointStack.total;
                        point.percentage = pointStack.total && (point.y / pointStack.total * 100);
                        point.stackY = yValue;

                        // Place the stack label
                        pointStack.setOffset(series.pointXOffset || 0, series.barW || 0);

                    }

                    // Set translated yBottom or remove it
                    point.yBottom = defined(yBottom) ?
                        yAxis.translate(yBottom, 0, 1, 0, 1) :
                        null;

                    // general hook, used for Highstock compare mode
                    if (hasModifyValue) {
                        yValue = series.modifyValue(yValue, point);
                    }

                    // Set the the plotY value, reset it for redraws
                    point.plotY = plotY = (typeof yValue === 'number' && yValue !== Infinity) ?
                        Math.min(Math.max(-1e5, yAxis.translate(yValue, 0, 1, 0, 1)), 1e5) : // #3201
                        undefined;

                    point.isInside = plotY !== undefined && plotY >= 0 && plotY <= yAxis.len && // #3519
                        plotX >= 0 && plotX <= xAxis.len;


                    // Set client related positions for mouse tracking
                    point.clientX = dynamicallyPlaced ? correctFloat(xAxis.translate(xValue, 0, 0, 0, 1, pointPlacement)) : plotX; // #1514, #5383, #5518

                    point.negative = point.y < (threshold || 0);

                    // some API data
                    point.category = categories && categories[point.x] !== undefined ?
                        categories[point.x] : point.x;

                    // Determine auto enabling of markers (#3635, #5099)
                    if (!point.isNull) {
                        if (lastPlotX !== undefined) {
                            closestPointRangePx = Math.min(closestPointRangePx, Math.abs(plotX - lastPlotX));
                        }
                        lastPlotX = plotX;
                    }

                }
                series.closestPointRangePx = closestPointRangePx;
            },

            /**
             * Return the series points with null points filtered out
             */
            getValidPoints: function(points, insideOnly) {
                var chart = this.chart;
                return grep(points || this.points || [], function isValidPoint(point) { // #3916, #5029
                    if (insideOnly && !chart.isInsidePlot(point.plotX, point.plotY, chart.inverted)) { // #5085
                        return false;
                    }
                    return !point.isNull;
                });
            },

            /**
             * Set the clipping for the series. For animated series it is called twice, first to initiate
             * animating the clip then the second time without the animation to set the final clip.
             */
            setClip: function(animation) {
                var chart = this.chart,
                    options = this.options,
                    renderer = chart.renderer,
                    inverted = chart.inverted,
                    seriesClipBox = this.clipBox,
                    clipBox = seriesClipBox || chart.clipBox,
                    sharedClipKey = this.sharedClipKey || ['_sharedClip', animation && animation.duration, animation && animation.easing, clipBox.height, options.xAxis, options.yAxis].join(','), // #4526
                    clipRect = chart[sharedClipKey],
                    markerClipRect = chart[sharedClipKey + 'm'];

                // If a clipping rectangle with the same properties is currently present in the chart, use that.
                if (!clipRect) {

                    // When animation is set, prepare the initial positions
                    if (animation) {
                        clipBox.width = 0;

                        chart[sharedClipKey + 'm'] = markerClipRect = renderer.clipRect(-99, // include the width of the first marker
                            inverted ? -chart.plotLeft : -chart.plotTop,
                            99,
                            inverted ? chart.chartWidth : chart.chartHeight
                        );
                    }
                    chart[sharedClipKey] = clipRect = renderer.clipRect(clipBox);
                    // Create hashmap for series indexes
                    clipRect.count = {
                        length: 0
                    };

                }
                if (animation) {
                    if (!clipRect.count[this.index]) {
                        clipRect.count[this.index] = true;
                        clipRect.count.length += 1;
                    }
                }

                if (options.clip !== false) {
                    this.group.clip(animation || seriesClipBox ? clipRect : chart.clipRect);
                    this.markerGroup.clip(markerClipRect);
                    this.sharedClipKey = sharedClipKey;
                }

                // Remove the shared clipping rectangle when all series are shown
                if (!animation) {
                    if (clipRect.count[this.index]) {
                        delete clipRect.count[this.index];
                        clipRect.count.length -= 1;
                    }

                    if (clipRect.count.length === 0 && sharedClipKey && chart[sharedClipKey]) {
                        if (!seriesClipBox) {
                            chart[sharedClipKey] = chart[sharedClipKey].destroy();
                        }
                        if (chart[sharedClipKey + 'm']) {
                            chart[sharedClipKey + 'm'] = chart[sharedClipKey + 'm'].destroy();
                        }
                    }
                }
            },

            /**
             * Animate in the series
             */
            animate: function(init) {
                var series = this,
                    chart = series.chart,
                    clipRect,
                    animation = animObject(series.options.animation),
                    sharedClipKey;

                // Initialize the animation. Set up the clipping rectangle.
                if (init) {

                    series.setClip(animation);

                    // Run the animation
                } else {
                    sharedClipKey = this.sharedClipKey;
                    clipRect = chart[sharedClipKey];
                    if (clipRect) {
                        clipRect.animate({
                            width: chart.plotSizeX
                        }, animation);
                    }
                    if (chart[sharedClipKey + 'm']) {
                        chart[sharedClipKey + 'm'].animate({
                            width: chart.plotSizeX + 99
                        }, animation);
                    }

                    // Delete this function to allow it only once
                    series.animate = null;

                }
            },

            /**
             * This runs after animation to land on the final plot clipping
             */
            afterAnimate: function() {
                this.setClip();
                fireEvent(this, 'afterAnimate');
            },

            /**
             * Draw the markers.
             *
             * @function #drawPoints
             * @memberOf Series
             * @returns {void}
             */
            drawPoints: function() {
                var series = this,
                    points = series.points,
                    chart = series.chart,
                    plotY,
                    i,
                    point,
                    symbol,
                    graphic,
                    options = series.options,
                    seriesMarkerOptions = options.marker,
                    pointMarkerOptions,
                    hasPointMarker,
                    enabled,
                    isInside,
                    markerGroup = series.markerGroup,
                    xAxis = series.xAxis,
                    markerAttribs,
                    globallyEnabled = pick(
                        seriesMarkerOptions.enabled,
                        xAxis.isRadial ? true : null,
                        series.closestPointRangePx > 2 * seriesMarkerOptions.radius
                    );

                if (seriesMarkerOptions.enabled !== false || series._hasPointMarkers) {

                    i = points.length;
                    while (i--) {
                        point = points[i];
                        plotY = point.plotY;
                        graphic = point.graphic;
                        pointMarkerOptions = point.marker || {};
                        hasPointMarker = !!point.marker;
                        enabled = (globallyEnabled && pointMarkerOptions.enabled === undefined) || pointMarkerOptions.enabled;
                        isInside = point.isInside;

                        // only draw the point if y is defined
                        if (enabled && isNumber(plotY) && point.y !== null) {

                            // Shortcuts
                            symbol = pick(pointMarkerOptions.symbol, series.symbol);
                            point.hasImage = symbol.indexOf('url') === 0;

                            markerAttribs = series.markerAttribs(
                                point,
                                point.selected && 'select'
                            );

                            if (graphic) { // update
                                graphic[isInside ? 'show' : 'hide'](true) // Since the marker group isn't clipped, each individual marker must be toggled
                                    .animate(markerAttribs);
                            } else if (isInside && (markerAttribs.width > 0 || point.hasImage)) {
                                point.graphic = graphic = chart.renderer.symbol(
                                        symbol,
                                        markerAttribs.x,
                                        markerAttribs.y,
                                        markerAttribs.width,
                                        markerAttribs.height,
                                        hasPointMarker ? pointMarkerOptions : seriesMarkerOptions
                                    )
                                    .add(markerGroup);
                            }


                            // Presentational attributes
                            if (graphic) {
                                graphic.attr(series.pointAttribs(point, point.selected && 'select'));
                            }


                            if (graphic) {
                                graphic.addClass(point.getClassName(), true);
                            }

                        } else if (graphic) {
                            point.graphic = graphic.destroy(); // #1269
                        }
                    }
                }

            },

            /**
             * Get non-presentational attributes for the point.
             */
            markerAttribs: function(point, state) {
                var seriesMarkerOptions = this.options.marker,
                    seriesStateOptions,
                    pointOptions = point && point.options,
                    pointMarkerOptions = (pointOptions && pointOptions.marker) || {},
                    pointStateOptions,
                    radius = pick(
                        pointMarkerOptions.radius,
                        seriesMarkerOptions.radius
                    ),
                    attribs;

                // Handle hover and select states
                if (state) {
                    seriesStateOptions = seriesMarkerOptions.states[state];
                    pointStateOptions = pointMarkerOptions.states &&
                        pointMarkerOptions.states[state];

                    radius = pick(
                        pointStateOptions && pointStateOptions.radius,
                        seriesStateOptions && seriesStateOptions.radius,
                        radius + (seriesStateOptions && seriesStateOptions.radiusPlus || 0)
                    );
                }

                if (point.hasImage) {
                    radius = 0; // and subsequently width and height is not set
                }

                attribs = {
                    x: Math.floor(point.plotX) - radius, // Math.floor for #1843
                    y: point.plotY - radius
                };

                if (radius) {
                    attribs.width = attribs.height = 2 * radius;
                }

                return attribs;

            },


            /**
             * Get presentational attributes for marker-based series (line, spline, scatter, bubble, mappoint...)
             */
            pointAttribs: function(point, state) {
                var seriesMarkerOptions = this.options.marker,
                    seriesStateOptions,
                    pointOptions = point && point.options,
                    pointMarkerOptions = (pointOptions && pointOptions.marker) || {},
                    pointStateOptions,
                    color = this.color,
                    pointColorOption = pointOptions && pointOptions.color,
                    pointColor = point && point.color,
                    strokeWidth = pick(
                        pointMarkerOptions.lineWidth,
                        seriesMarkerOptions.lineWidth
                    ),
                    zoneColor,
                    fill,
                    stroke,
                    zone;

                if (point && this.zones.length) {
                    zone = point.getZone();
                    if (zone && zone.color) {
                        zoneColor = zone.color;
                    }
                }

                color = pointColorOption || zoneColor || pointColor || color;
                fill = pointMarkerOptions.fillColor || seriesMarkerOptions.fillColor || color;
                stroke = pointMarkerOptions.lineColor || seriesMarkerOptions.lineColor || color;

                // Handle hover and select states
                if (state) {
                    seriesStateOptions = seriesMarkerOptions.states[state];
                    pointStateOptions = (pointMarkerOptions.states && pointMarkerOptions.states[state]) || {};
                    strokeWidth = pick(
                        pointStateOptions.lineWidth,
                        seriesStateOptions.lineWidth,
                        strokeWidth + pick(
                            pointStateOptions.lineWidthPlus,
                            seriesStateOptions.lineWidthPlus,
                            0
                        )
                    );
                    fill = pointStateOptions.fillColor || seriesStateOptions.fillColor || fill;
                    stroke = pointStateOptions.lineColor || seriesStateOptions.lineColor || stroke;
                }

                return {
                    'stroke': stroke,
                    'stroke-width': strokeWidth,
                    'fill': fill
                };
            },

            /**
             * Clear DOM objects and free up memory
             */
            destroy: function() {
                var series = this,
                    chart = series.chart,
                    issue134 = /AppleWebKit\/533/.test(win.navigator.userAgent),
                    destroy,
                    i,
                    data = series.data || [],
                    point,
                    prop,
                    axis;

                // add event hook
                fireEvent(series, 'destroy');

                // remove all events
                removeEvent(series);

                // erase from axes
                each(series.axisTypes || [], function(AXIS) {
                    axis = series[AXIS];
                    if (axis && axis.series) {
                        erase(axis.series, series);
                        axis.isDirty = axis.forceRedraw = true;
                    }
                });

                // remove legend items
                if (series.legendItem) {
                    series.chart.legend.destroyItem(series);
                }

                // destroy all points with their elements
                i = data.length;
                while (i--) {
                    point = data[i];
                    if (point && point.destroy) {
                        point.destroy();
                    }
                }
                series.points = null;

                // Clear the animation timeout if we are destroying the series during initial animation
                clearTimeout(series.animationTimeout);

                // Destroy all SVGElements associated to the series
                for (prop in series) {
                    if (series[prop] instanceof SVGElement && !series[prop].survive) { // Survive provides a hook for not destroying

                        // issue 134 workaround
                        destroy = issue134 && prop === 'group' ?
                            'hide' :
                            'destroy';

                        series[prop][destroy]();
                    }
                }

                // remove from hoverSeries
                if (chart.hoverSeries === series) {
                    chart.hoverSeries = null;
                }
                erase(chart.series, series);

                // clear all members
                for (prop in series) {
                    delete series[prop];
                }
            },

            /**
             * Get the graph path
             */
            getGraphPath: function(points, nullsAsZeroes, connectCliffs) {
                var series = this,
                    options = series.options,
                    step = options.step,
                    reversed,
                    graphPath = [],
                    xMap = [],
                    gap;

                points = points || series.points;

                // Bottom of a stack is reversed
                reversed = points.reversed;
                if (reversed) {
                    points.reverse();
                }
                // Reverse the steps (#5004)
                step = {
                    right: 1,
                    center: 2
                }[step] || (step && 3);
                if (step && reversed) {
                    step = 4 - step;
                }

                // Remove invalid points, especially in spline (#5015)
                if (options.connectNulls && !nullsAsZeroes && !connectCliffs) {
                    points = this.getValidPoints(points);
                }

                // Build the line
                each(points, function(point, i) {

                    var plotX = point.plotX,
                        plotY = point.plotY,
                        lastPoint = points[i - 1],
                        pathToPoint; // the path to this point from the previous

                    if ((point.leftCliff || (lastPoint && lastPoint.rightCliff)) && !connectCliffs) {
                        gap = true; // ... and continue
                    }

                    // Line series, nullsAsZeroes is not handled
                    if (point.isNull && !defined(nullsAsZeroes) && i > 0) {
                        gap = !options.connectNulls;

                        // Area series, nullsAsZeroes is set
                    } else if (point.isNull && !nullsAsZeroes) {
                        gap = true;

                    } else {

                        if (i === 0 || gap) {
                            pathToPoint = ['M', point.plotX, point.plotY];

                        } else if (series.getPointSpline) { // generate the spline as defined in the SplineSeries object

                            pathToPoint = series.getPointSpline(points, point, i);

                        } else if (step) {

                            if (step === 1) { // right
                                pathToPoint = [
                                    'L',
                                    lastPoint.plotX,
                                    plotY
                                ];

                            } else if (step === 2) { // center
                                pathToPoint = [
                                    'L',
                                    (lastPoint.plotX + plotX) / 2,
                                    lastPoint.plotY,
                                    'L',
                                    (lastPoint.plotX + plotX) / 2,
                                    plotY
                                ];

                            } else {
                                pathToPoint = [
                                    'L',
                                    plotX,
                                    lastPoint.plotY
                                ];
                            }
                            pathToPoint.push('L', plotX, plotY);

                        } else {
                            // normal line to next point
                            pathToPoint = [
                                'L',
                                plotX,
                                plotY
                            ];
                        }

                        // Prepare for animation. When step is enabled, there are two path nodes for each x value.
                        xMap.push(point.x);
                        if (step) {
                            xMap.push(point.x);
                        }

                        graphPath.push.apply(graphPath, pathToPoint);
                        gap = false;
                    }
                });

                graphPath.xMap = xMap;
                series.graphPath = graphPath;

                return graphPath;

            },

            /**
             * Draw the actual graph
             */
            drawGraph: function() {
                var series = this,
                    options = this.options,
                    graphPath = (this.gappedPath || this.getGraphPath).call(this),
                    props = [
                        [
                            'graph',
                            'highcharts-graph',

                            options.lineColor || this.color,
                            options.dashStyle

                        ]
                    ];

                // Add the zone properties if any
                each(this.zones, function(zone, i) {
                    props.push([
                        'zone-graph-' + i,
                        'highcharts-graph highcharts-zone-graph-' + i + ' ' + (zone.className || ''),

                        zone.color || series.color,
                        zone.dashStyle || options.dashStyle

                    ]);
                });

                // Draw the graph
                each(props, function(prop, i) {
                    var graphKey = prop[0],
                        graph = series[graphKey],
                        attribs;

                    if (graph) {
                        graph.endX = graphPath.xMap;
                        graph.animate({
                            d: graphPath
                        });

                    } else if (graphPath.length) { // #1487

                        series[graphKey] = series.chart.renderer.path(graphPath)
                            .addClass(prop[1])
                            .attr({
                                zIndex: 1
                            }) // #1069
                            .add(series.group);


                        attribs = {
                            'stroke': prop[2],
                            'stroke-width': options.lineWidth,
                            'fill': (series.fillGraph && series.color) || 'none' // Polygon series use filled graph
                        };

                        if (prop[3]) {
                            attribs.dashstyle = prop[3];
                        } else if (options.linecap !== 'square') {
                            attribs['stroke-linecap'] = attribs['stroke-linejoin'] = 'round';
                        }

                        graph = series[graphKey]
                            .attr(attribs)
                            .shadow((i < 2) && options.shadow); // add shadow to normal series (0) or to first zone (1) #3932

                    }

                    // Helpers for animation
                    if (graph) {
                        graph.startX = graphPath.xMap;
                        //graph.shiftUnit = options.step ? 2 : 1;
                        graph.isArea = graphPath.isArea; // For arearange animation
                    }
                });
            },

            /**
             * Clip the graphs into the positive and negative coloured graphs
             */
            applyZones: function() {
                var series = this,
                    chart = this.chart,
                    renderer = chart.renderer,
                    zones = this.zones,
                    translatedFrom,
                    translatedTo,
                    clips = this.clips || [],
                    clipAttr,
                    graph = this.graph,
                    area = this.area,
                    chartSizeMax = Math.max(chart.chartWidth, chart.chartHeight),
                    axis = this[(this.zoneAxis || 'y') + 'Axis'],
                    extremes,
                    reversed,
                    inverted = chart.inverted,
                    horiz,
                    pxRange,
                    pxPosMin,
                    pxPosMax,
                    ignoreZones = false;

                if (zones.length && (graph || area) && axis && axis.min !== undefined) {
                    reversed = axis.reversed;
                    horiz = axis.horiz;
                    // The use of the Color Threshold assumes there are no gaps
                    // so it is safe to hide the original graph and area
                    if (graph) {
                        graph.hide();
                    }
                    if (area) {
                        area.hide();
                    }

                    // Create the clips
                    extremes = axis.getExtremes();
                    each(zones, function(threshold, i) {

                        translatedFrom = reversed ?
                            (horiz ? chart.plotWidth : 0) :
                            (horiz ? 0 : axis.toPixels(extremes.min));
                        translatedFrom = Math.min(Math.max(pick(translatedTo, translatedFrom), 0), chartSizeMax);
                        translatedTo = Math.min(Math.max(Math.round(axis.toPixels(pick(threshold.value, extremes.max), true)), 0), chartSizeMax);

                        if (ignoreZones) {
                            translatedFrom = translatedTo = axis.toPixels(extremes.max);
                        }

                        pxRange = Math.abs(translatedFrom - translatedTo);
                        pxPosMin = Math.min(translatedFrom, translatedTo);
                        pxPosMax = Math.max(translatedFrom, translatedTo);
                        if (axis.isXAxis) {
                            clipAttr = {
                                x: inverted ? pxPosMax : pxPosMin,
                                y: 0,
                                width: pxRange,
                                height: chartSizeMax
                            };
                            if (!horiz) {
                                clipAttr.x = chart.plotHeight - clipAttr.x;
                            }
                        } else {
                            clipAttr = {
                                x: 0,
                                y: inverted ? pxPosMax : pxPosMin,
                                width: chartSizeMax,
                                height: pxRange
                            };
                            if (horiz) {
                                clipAttr.y = chart.plotWidth - clipAttr.y;
                            }
                        }


                        /// VML SUPPPORT
                        if (inverted && renderer.isVML) {
                            if (axis.isXAxis) {
                                clipAttr = {
                                    x: 0,
                                    y: reversed ? pxPosMin : pxPosMax,
                                    height: clipAttr.width,
                                    width: chart.chartWidth
                                };
                            } else {
                                clipAttr = {
                                    x: clipAttr.y - chart.plotLeft - chart.spacingBox.x,
                                    y: 0,
                                    width: clipAttr.height,
                                    height: chart.chartHeight
                                };
                            }
                        }
                        /// END OF VML SUPPORT


                        if (clips[i]) {
                            clips[i].animate(clipAttr);
                        } else {
                            clips[i] = renderer.clipRect(clipAttr);

                            if (graph) {
                                series['zone-graph-' + i].clip(clips[i]);
                            }

                            if (area) {
                                series['zone-area-' + i].clip(clips[i]);
                            }
                        }
                        // if this zone extends out of the axis, ignore the others
                        ignoreZones = threshold.value > extremes.max;
                    });
                    this.clips = clips;
                }
            },

            /**
             * Initialize and perform group inversion on series.group and series.markerGroup
             */
            invertGroups: function(inverted) {
                var series = this,
                    chart = series.chart,
                    remover;

                function setInvert() {
                    var size = {
                        width: series.yAxis.len,
                        height: series.xAxis.len
                    };

                    each(['group', 'markerGroup'], function(groupName) {
                        if (series[groupName]) {
                            series[groupName].attr(size).invert(inverted);
                        }
                    });
                }

                // Pie, go away (#1736)
                if (!series.xAxis) {
                    return;
                }

                // A fixed size is needed for inversion to work
                remover = addEvent(chart, 'resize', setInvert);
                addEvent(series, 'destroy', remover);

                // Do it now
                setInvert(inverted); // do it now

                // On subsequent render and redraw, just do setInvert without setting up events again
                series.invertGroups = setInvert;
            },

            /**
             * General abstraction for creating plot groups like series.group, series.dataLabelsGroup and
             * series.markerGroup. On subsequent calls, the group will only be adjusted to the updated plot size.
             */
            plotGroup: function(prop, name, visibility, zIndex, parent) {
                var group = this[prop],
                    isNew = !group;

                // Generate it on first call
                if (isNew) {
                    this[prop] = group = this.chart.renderer.g(name)
                        .attr({
                            zIndex: zIndex || 0.1 // IE8 and pointer logic use this
                        })
                        .add(parent);

                    group.addClass('highcharts-series-' + this.index + ' highcharts-' + this.type + '-series highcharts-color-' + this.colorIndex +
                        ' ' + (this.options.className || ''));
                }

                // Place it on first and subsequent (redraw) calls
                group.attr({
                    visibility: visibility
                })[isNew ? 'attr' : 'animate'](this.getPlotBox());
                return group;
            },

            /**
             * Get the translation and scale for the plot area of this series
             */
            getPlotBox: function() {
                var chart = this.chart,
                    xAxis = this.xAxis,
                    yAxis = this.yAxis;

                // Swap axes for inverted (#2339)
                if (chart.inverted) {
                    xAxis = yAxis;
                    yAxis = this.xAxis;
                }
                return {
                    translateX: xAxis ? xAxis.left : chart.plotLeft,
                    translateY: yAxis ? yAxis.top : chart.plotTop,
                    scaleX: 1, // #1623
                    scaleY: 1
                };
            },

            /**
             * Render the graph and markers
             */
            render: function() {
                var series = this,
                    chart = series.chart,
                    group,
                    options = series.options,
                    // Animation doesn't work in IE8 quirks when the group div is hidden,
                    // and looks bad in other oldIE
                    animDuration = !!series.animate && chart.renderer.isSVG && animObject(options.animation).duration,
                    visibility = series.visible ? 'inherit' : 'hidden', // #2597
                    zIndex = options.zIndex,
                    hasRendered = series.hasRendered,
                    chartSeriesGroup = chart.seriesGroup,
                    inverted = chart.inverted;

                // the group
                group = series.plotGroup(
                    'group',
                    'series',
                    visibility,
                    zIndex,
                    chartSeriesGroup
                );

                series.markerGroup = series.plotGroup(
                    'markerGroup',
                    'markers',
                    visibility,
                    zIndex,
                    chartSeriesGroup
                );

                // initiate the animation
                if (animDuration) {
                    series.animate(true);
                }

                // SVGRenderer needs to know this before drawing elements (#1089, #1795)
                group.inverted = series.isCartesian ? inverted : false;

                // draw the graph if any
                if (series.drawGraph) {
                    series.drawGraph();
                    series.applyZones();
                }

                /*		each(series.points, function (point) {
                			if (point.redraw) {
                				point.redraw();
                			}
                		});*/

                // draw the data labels (inn pies they go before the points)
                if (series.drawDataLabels) {
                    series.drawDataLabels();
                }

                // draw the points
                if (series.visible) {
                    series.drawPoints();
                }


                // draw the mouse tracking area
                if (series.drawTracker && series.options.enableMouseTracking !== false) {
                    series.drawTracker();
                }

                // Handle inverted series and tracker groups
                series.invertGroups(inverted);

                // Initial clipping, must be defined after inverting groups for VML. Applies to columns etc. (#3839).
                if (options.clip !== false && !series.sharedClipKey && !hasRendered) {
                    group.clip(chart.clipRect);
                }

                // Run the animation
                if (animDuration) {
                    series.animate();
                }

                // Call the afterAnimate function on animation complete (but don't overwrite the animation.complete option
                // which should be available to the user).
                if (!hasRendered) {
                    series.animationTimeout = syncTimeout(function() {
                        series.afterAnimate();
                    }, animDuration);
                }

                series.isDirty = series.isDirtyData = false; // means data is in accordance with what you see
                // (See #322) series.isDirty = series.isDirtyData = false; // means data is in accordance with what you see
                series.hasRendered = true;
            },

            /**
             * Redraw the series after an update in the axes.
             */
            redraw: function() {
                var series = this,
                    chart = series.chart,
                    wasDirty = series.isDirty || series.isDirtyData, // cache it here as it is set to false in render, but used after
                    group = series.group,
                    xAxis = series.xAxis,
                    yAxis = series.yAxis;

                // reposition on resize
                if (group) {
                    if (chart.inverted) {
                        group.attr({
                            width: chart.plotWidth,
                            height: chart.plotHeight
                        });
                    }

                    group.animate({
                        translateX: pick(xAxis && xAxis.left, chart.plotLeft),
                        translateY: pick(yAxis && yAxis.top, chart.plotTop)
                    });
                }

                series.translate();
                series.render();
                if (wasDirty) { // #3868, #3945
                    delete this.kdTree;
                }
            },

            /**
             * KD Tree && PointSearching Implementation
             */

            kdDimensions: 1,
            kdAxisArray: ['clientX', 'plotY'],

            searchPoint: function(e, compareX) {
                var series = this,
                    xAxis = series.xAxis,
                    yAxis = series.yAxis,
                    inverted = series.chart.inverted;

                return this.searchKDTree({
                    clientX: inverted ? xAxis.len - e.chartY + xAxis.pos : e.chartX - xAxis.pos,
                    plotY: inverted ? yAxis.len - e.chartX + yAxis.pos : e.chartY - yAxis.pos
                }, compareX);
            },

            buildKDTree: function() {
                var series = this,
                    dimensions = series.kdDimensions;

                // Internal function
                function _kdtree(points, depth, dimensions) {
                    var axis,
                        median,
                        length = points && points.length;

                    if (length) {

                        // alternate between the axis
                        axis = series.kdAxisArray[depth % dimensions];

                        // sort point array
                        points.sort(function(a, b) {
                            return a[axis] - b[axis];
                        });

                        median = Math.floor(length / 2);

                        // build and return nod
                        return {
                            point: points[median],
                            left: _kdtree(points.slice(0, median), depth + 1, dimensions),
                            right: _kdtree(points.slice(median + 1), depth + 1, dimensions)
                        };

                    }
                }

                // Start the recursive build process with a clone of the points array and null points filtered out (#3873)
                function startRecursive() {
                    series.kdTree = _kdtree(
                        series.getValidPoints(
                            null, !series.directTouch // For line-type series restrict to plot area, but column-type series not (#3916, #4511)
                        ),
                        dimensions,
                        dimensions
                    );
                }
                delete series.kdTree;

                // For testing tooltips, don't build async
                syncTimeout(startRecursive, series.options.kdNow ? 0 : 1);
            },

            searchKDTree: function(point, compareX) {
                var series = this,
                    kdX = this.kdAxisArray[0],
                    kdY = this.kdAxisArray[1],
                    kdComparer = compareX ? 'distX' : 'dist';

                // Set the one and two dimensional distance on the point object
                function setDistance(p1, p2) {
                    var x = (defined(p1[kdX]) && defined(p2[kdX])) ? Math.pow(p1[kdX] - p2[kdX], 2) : null,
                        y = (defined(p1[kdY]) && defined(p2[kdY])) ? Math.pow(p1[kdY] - p2[kdY], 2) : null,
                        r = (x || 0) + (y || 0);

                    p2.dist = defined(r) ? Math.sqrt(r) : Number.MAX_VALUE;
                    p2.distX = defined(x) ? Math.sqrt(x) : Number.MAX_VALUE;
                }

                function _search(search, tree, depth, dimensions) {
                    var point = tree.point,
                        axis = series.kdAxisArray[depth % dimensions],
                        tdist,
                        sideA,
                        sideB,
                        ret = point,
                        nPoint1,
                        nPoint2;

                    setDistance(search, point);

                    // Pick side based on distance to splitting point
                    tdist = search[axis] - point[axis];
                    sideA = tdist < 0 ? 'left' : 'right';
                    sideB = tdist < 0 ? 'right' : 'left';

                    // End of tree
                    if (tree[sideA]) {
                        nPoint1 = _search(search, tree[sideA], depth + 1, dimensions);

                        ret = (nPoint1[kdComparer] < ret[kdComparer] ? nPoint1 : point);
                    }
                    if (tree[sideB]) {
                        // compare distance to current best to splitting point to decide wether to check side B or not
                        if (Math.sqrt(tdist * tdist) < ret[kdComparer]) {
                            nPoint2 = _search(search, tree[sideB], depth + 1, dimensions);
                            ret = (nPoint2[kdComparer] < ret[kdComparer] ? nPoint2 : ret);
                        }
                    }

                    return ret;
                }

                if (!this.kdTree) {
                    this.buildKDTree();
                }

                if (this.kdTree) {
                    return _search(point,
                        this.kdTree, this.kdDimensions, this.kdDimensions);
                }
            }

        }); // end Series prototype

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var Axis = H.Axis,
            Chart = H.Chart,
            correctFloat = H.correctFloat,
            defined = H.defined,
            destroyObjectProperties = H.destroyObjectProperties,
            each = H.each,
            format = H.format,
            pick = H.pick,
            Series = H.Series;

        /**
         * The class for stacks. Each stack, on a specific X value and either negative
         * or positive, has its own stack item.
         *
         * @class
         */
        function StackItem(axis, options, isNegative, x, stackOption) {

            var inverted = axis.chart.inverted;

            this.axis = axis;

            // Tells if the stack is negative
            this.isNegative = isNegative;

            // Save the options to be able to style the label
            this.options = options;

            // Save the x value to be able to position the label later
            this.x = x;

            // Initialize total value
            this.total = null;

            // This will keep each points' extremes stored by series.index and point 
            // index
            this.points = {};

            // Save the stack option on the series configuration object, and whether to 
            // treat it as percent
            this.stack = stackOption;
            this.leftCliff = 0;
            this.rightCliff = 0;

            // The align options and text align varies on whether the stack is negative 
            // and if the chart is inverted or not.
            // First test the user supplied value, then use the dynamic.
            this.alignOptions = {
                align: options.align ||
                    (inverted ? (isNegative ? 'left' : 'right') : 'center'),
                verticalAlign: options.verticalAlign ||
                    (inverted ? 'middle' : (isNegative ? 'bottom' : 'top')),
                y: pick(options.y, inverted ? 4 : (isNegative ? 14 : -6)),
                x: pick(options.x, inverted ? (isNegative ? -6 : 6) : 0)
            };

            this.textAlign = options.textAlign ||
                (inverted ? (isNegative ? 'right' : 'left') : 'center');
        }

        StackItem.prototype = {
            destroy: function() {
                destroyObjectProperties(this, this.axis);
            },

            /**
             * Renders the stack total label and adds it to the stack label group.
             */
            render: function(group) {
                var options = this.options,
                    formatOption = options.format,
                    str = formatOption ?
                    format(formatOption, this) :
                    options.formatter.call(this); // format the text in the label

                // Change the text to reflect the new total and set visibility to hidden
                // in case the serie is hidden
                if (this.label) {
                    this.label.attr({
                        text: str,
                        visibility: 'hidden'
                    });
                    // Create new label
                } else {
                    this.label =
                        this.axis.chart.renderer.text(str, null, null, options.useHTML)
                        .css(options.style)
                        .attr({
                            align: this.textAlign,
                            rotation: options.rotation,
                            visibility: 'hidden' // hidden until setOffset is called
                        })
                        .add(group); // add to the labels-group
                }
            },

            /**
             * Sets the offset that the stack has from the x value and repositions the
             * label.
             */
            setOffset: function(xOffset, xWidth) {
                var stackItem = this,
                    axis = stackItem.axis,
                    chart = axis.chart,
                    inverted = chart.inverted,
                    reversed = axis.reversed,
                    neg = (this.isNegative && !reversed) ||
                    (!this.isNegative && reversed), // #4056
                    // stack value translated mapped to chart coordinates
                    y = axis.translate(
                        axis.usePercentage ? 100 : this.total,
                        0,
                        0,
                        0,
                        1
                    ),
                    yZero = axis.translate(0), // stack origin
                    h = Math.abs(y - yZero), // stack height
                    x = chart.xAxis[0].translate(this.x) + xOffset, // stack x position
                    plotHeight = chart.plotHeight,
                    stackBox = { // this is the box for the complete stack
                        x: inverted ? (neg ? y : y - h) : x,
                        y: inverted ?
                            plotHeight - x - xWidth : (neg ? (plotHeight - y - h) :
                                plotHeight - y),
                        width: inverted ? h : xWidth,
                        height: inverted ? xWidth : h
                    },
                    label = this.label,
                    alignAttr;

                if (label) {
                    // Align the label to the box
                    label.align(this.alignOptions, null, stackBox);

                    // Set visibility (#678)
                    alignAttr = label.alignAttr;
                    label[
                        this.options.crop === false || chart.isInsidePlot(
                            alignAttr.x,
                            alignAttr.y
                        ) ? 'show' : 'hide'](true);
                }
            }
        };

        /**
         * Generate stacks for each series and calculate stacks total values
         */
        Chart.prototype.getStacks = function() {
            var chart = this;

            // reset stacks for each yAxis
            each(chart.yAxis, function(axis) {
                if (axis.stacks && axis.hasVisibleSeries) {
                    axis.oldStacks = axis.stacks;
                }
            });

            each(chart.series, function(series) {
                if (series.options.stacking && (series.visible === true ||
                        chart.options.chart.ignoreHiddenSeries === false)) {
                    series.stackKey = series.type + pick(series.options.stack, '');
                }
            });
        };


        // Stacking methods defined on the Axis prototype

        /**
         * Build the stacks from top down
         */
        Axis.prototype.buildStacks = function() {
            var axisSeries = this.series,
                series,
                reversedStacks = pick(this.options.reversedStacks, true),
                len = axisSeries.length,
                i;
            if (!this.isXAxis) {
                this.usePercentage = false;
                i = len;
                while (i--) {
                    axisSeries[reversedStacks ? i : len - i - 1].setStackedPoints();
                }

                i = len;
                while (i--) {
                    series = axisSeries[reversedStacks ? i : len - i - 1];
                    if (series.setStackCliffs) {
                        series.setStackCliffs();
                    }
                }
                // Loop up again to compute percent stack
                if (this.usePercentage) {
                    for (i = 0; i < len; i++) {
                        axisSeries[i].setPercentStacks();
                    }
                }
            }
        };

        Axis.prototype.renderStackTotals = function() {
            var axis = this,
                chart = axis.chart,
                renderer = chart.renderer,
                stacks = axis.stacks,
                stackKey,
                oneStack,
                stackCategory,
                stackTotalGroup = axis.stackTotalGroup;

            // Create a separate group for the stack total labels
            if (!stackTotalGroup) {
                axis.stackTotalGroup = stackTotalGroup =
                    renderer.g('stack-labels')
                    .attr({
                        visibility: 'visible',
                        zIndex: 6
                    })
                    .add();
            }

            // plotLeft/Top will change when y axis gets wider so we need to translate
            // the stackTotalGroup at every render call. See bug #506 and #516
            stackTotalGroup.translate(chart.plotLeft, chart.plotTop);

            // Render each stack total
            for (stackKey in stacks) {
                oneStack = stacks[stackKey];
                for (stackCategory in oneStack) {
                    oneStack[stackCategory].render(stackTotalGroup);
                }
            }
        };

        /**
         * Set all the stacks to initial states and destroy unused ones.
         */
        Axis.prototype.resetStacks = function() {
            var stacks = this.stacks,
                type,
                i;
            if (!this.isXAxis) {
                for (type in stacks) {
                    for (i in stacks[type]) {

                        // Clean up memory after point deletion (#1044, #4320)
                        if (stacks[type][i].touched < this.stacksTouched) {
                            stacks[type][i].destroy();
                            delete stacks[type][i];

                            // Reset stacks
                        } else {
                            stacks[type][i].total = null;
                            stacks[type][i].cum = 0;
                        }
                    }
                }
            }
        };

        Axis.prototype.cleanStacks = function() {
            var stacks, type, i;

            if (!this.isXAxis) {
                if (this.oldStacks) {
                    stacks = this.stacks = this.oldStacks;
                }

                // reset stacks
                for (type in stacks) {
                    for (i in stacks[type]) {
                        stacks[type][i].cum = stacks[type][i].total;
                    }
                }
            }
        };


        // Stacking methods defnied for Series prototype

        /**
         * Adds series' points value to corresponding stack
         */
        Series.prototype.setStackedPoints = function() {
            if (!this.options.stacking || (this.visible !== true &&
                    this.chart.options.chart.ignoreHiddenSeries !== false)) {
                return;
            }

            var series = this,
                xData = series.processedXData,
                yData = series.processedYData,
                stackedYData = [],
                yDataLength = yData.length,
                seriesOptions = series.options,
                threshold = seriesOptions.threshold,
                stackThreshold = seriesOptions.startFromThreshold ? threshold : 0,
                stackOption = seriesOptions.stack,
                stacking = seriesOptions.stacking,
                stackKey = series.stackKey,
                negKey = '-' + stackKey,
                negStacks = series.negStacks,
                yAxis = series.yAxis,
                stacks = yAxis.stacks,
                oldStacks = yAxis.oldStacks,
                stackIndicator,
                isNegative,
                stack,
                other,
                key,
                pointKey,
                i,
                x,
                y;


            yAxis.stacksTouched += 1;

            // loop over the non-null y values and read them into a local array
            for (i = 0; i < yDataLength; i++) {
                x = xData[i];
                y = yData[i];
                stackIndicator = series.getStackIndicator(
                    stackIndicator,
                    x,
                    series.index
                );
                pointKey = stackIndicator.key;
                // Read stacked values into a stack based on the x value,
                // the sign of y and the stack key. Stacking is also handled for null
                // values (#739)
                isNegative = negStacks && y < (stackThreshold ? 0 : threshold);
                key = isNegative ? negKey : stackKey;

                // Create empty object for this stack if it doesn't exist yet
                if (!stacks[key]) {
                    stacks[key] = {};
                }

                // Initialize StackItem for this x
                if (!stacks[key][x]) {
                    if (oldStacks[key] && oldStacks[key][x]) {
                        stacks[key][x] = oldStacks[key][x];
                        stacks[key][x].total = null;
                    } else {
                        stacks[key][x] = new StackItem(
                            yAxis,
                            yAxis.options.stackLabels,
                            isNegative,
                            x,
                            stackOption
                        );
                    }
                }

                // If the StackItem doesn't exist, create it first
                stack = stacks[key][x];
                if (y !== null) {
                    stack.points[pointKey] = stack.points[series.index] = [pick(stack.cum, stackThreshold)];

                    // Record the base of the stack
                    if (!defined(stack.cum)) {
                        stack.base = pointKey;
                    }
                    stack.touched = yAxis.stacksTouched;


                    // In area charts, if there are multiple points on the same X value,
                    // let the area fill the full span of those points
                    if (stackIndicator.index > 0 && series.singleStacks === false) {
                        stack.points[pointKey][0] =
                            stack.points[series.index + ',' + x + ',0'][0];
                    }
                }

                // Add value to the stack total
                if (stacking === 'percent') {

                    // Percent stacked column, totals are the same for the positive and
                    // negative stacks
                    other = isNegative ? stackKey : negKey;
                    if (negStacks && stacks[other] && stacks[other][x]) {
                        other = stacks[other][x];
                        stack.total = other.total =
                            Math.max(other.total, stack.total) + Math.abs(y) || 0;

                        // Percent stacked areas
                    } else {
                        stack.total = correctFloat(stack.total + (Math.abs(y) || 0));
                    }
                } else {
                    stack.total = correctFloat(stack.total + (y || 0));
                }

                stack.cum = pick(stack.cum, stackThreshold) + (y || 0);

                if (y !== null) {
                    stack.points[pointKey].push(stack.cum);
                    stackedYData[i] = stack.cum;
                }

            }

            if (stacking === 'percent') {
                yAxis.usePercentage = true;
            }

            this.stackedYData = stackedYData; // To be used in getExtremes

            // Reset old stacks
            yAxis.oldStacks = {};
        };

        /**
         * Iterate over all stacks and compute the absolute values to percent
         */
        Series.prototype.setPercentStacks = function() {
            var series = this,
                stackKey = series.stackKey,
                stacks = series.yAxis.stacks,
                processedXData = series.processedXData,
                stackIndicator;

            each([stackKey, '-' + stackKey], function(key) {
                var i = processedXData.length,
                    x,
                    stack,
                    pointExtremes,
                    totalFactor;

                while (i--) {
                    x = processedXData[i];
                    stackIndicator = series.getStackIndicator(
                        stackIndicator,
                        x,
                        series.index,
                        key
                    );
                    stack = stacks[key] && stacks[key][x];
                    pointExtremes = stack && stack.points[stackIndicator.key];
                    if (pointExtremes) {
                        totalFactor = stack.total ? 100 / stack.total : 0;
                        // Y bottom value
                        pointExtremes[0] = correctFloat(pointExtremes[0] * totalFactor);
                        // Y value
                        pointExtremes[1] = correctFloat(pointExtremes[1] * totalFactor);
                        series.stackedYData[i] = pointExtremes[1];
                    }
                }
            });
        };

        /**
         * Get stack indicator, according to it's x-value, to determine points with the
         * same x-value
         */
        Series.prototype.getStackIndicator = function(stackIndicator, x, index, key) {
            // Update stack indicator, when:
            // first point in a stack || x changed || stack type (negative vs positive)
            // changed:
            if (!defined(stackIndicator) || stackIndicator.x !== x ||
                (key && stackIndicator.key !== key)) {
                stackIndicator = {
                    x: x,
                    index: 0,
                    key: key
                };
            } else {
                stackIndicator.index++;
            }

            stackIndicator.key = [index, x, stackIndicator.index].join(',');

            return stackIndicator;
        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var addEvent = H.addEvent,
            animate = H.animate,
            Axis = H.Axis,
            Chart = H.Chart,
            createElement = H.createElement,
            css = H.css,
            defined = H.defined,
            each = H.each,
            erase = H.erase,
            extend = H.extend,
            fireEvent = H.fireEvent,
            inArray = H.inArray,
            isNumber = H.isNumber,
            isObject = H.isObject,
            merge = H.merge,
            pick = H.pick,
            Point = H.Point,
            Series = H.Series,
            seriesTypes = H.seriesTypes,
            setAnimation = H.setAnimation,
            splat = H.splat;

        // Extend the Chart prototype for dynamic methods
        extend(Chart.prototype, /** @lends Highcharts.Chart.prototype */ {

            /**
             * Add a series dynamically after  time
             *
             * @param {Object} options The config options
             * @param {Boolean} redraw Whether to redraw the chart after adding. Defaults to true.
             * @param {Boolean|Object} animation Whether to apply animation, and optionally animation
             *    configuration
             *
             * @return {Object} series The newly created series object
             */
            addSeries: function(options, redraw, animation) {
                var series,
                    chart = this;

                if (options) {
                    redraw = pick(redraw, true); // defaults to true

                    fireEvent(chart, 'addSeries', {
                        options: options
                    }, function() {
                        series = chart.initSeries(options);

                        chart.isDirtyLegend = true; // the series array is out of sync with the display
                        chart.linkSeries();
                        if (redraw) {
                            chart.redraw(animation);
                        }
                    });
                }

                return series;
            },

            /**
             * Add an axis to the chart
             * @param {Object} options The axis option
             * @param {Boolean} isX Whether it is an X axis or a value axis
             */
            addAxis: function(options, isX, redraw, animation) {
                var key = isX ? 'xAxis' : 'yAxis',
                    chartOptions = this.options,
                    userOptions = merge(options, {
                        index: this[key].length,
                        isX: isX
                    });

                new Axis(this, userOptions); // eslint-disable-line no-new

                // Push the new axis options to the chart options
                chartOptions[key] = splat(chartOptions[key] || {});
                chartOptions[key].push(userOptions);

                if (pick(redraw, true)) {
                    this.redraw(animation);
                }
            },

            /**
             * Dim the chart and show a loading text or symbol
             * @param {String} str An optional text to show in the loading label instead of the default one
             */
            showLoading: function(str) {
                var chart = this,
                    options = chart.options,
                    loadingDiv = chart.loadingDiv,
                    loadingOptions = options.loading,
                    setLoadingSize = function() {
                        if (loadingDiv) {
                            css(loadingDiv, {
                                left: chart.plotLeft + 'px',
                                top: chart.plotTop + 'px',
                                width: chart.plotWidth + 'px',
                                height: chart.plotHeight + 'px'
                            });
                        }
                    };

                // create the layer at the first call
                if (!loadingDiv) {
                    chart.loadingDiv = loadingDiv = createElement('div', {
                        className: 'highcharts-loading highcharts-loading-hidden'
                    }, null, chart.container);

                    chart.loadingSpan = createElement(
                        'span', {
                            className: 'highcharts-loading-inner'
                        },
                        null,
                        loadingDiv
                    );
                    addEvent(chart, 'redraw', setLoadingSize); // #1080
                }

                loadingDiv.className = 'highcharts-loading';

                // Update text
                chart.loadingSpan.innerHTML = str || options.lang.loading;


                // Update visuals
                css(loadingDiv, extend(loadingOptions.style, {
                    zIndex: 10
                }));
                css(chart.loadingSpan, loadingOptions.labelStyle);

                // Show it
                if (!chart.loadingShown) {
                    css(loadingDiv, {
                        opacity: 0,
                        display: ''
                    });
                    animate(loadingDiv, {
                        opacity: loadingOptions.style.opacity || 0.5
                    }, {
                        duration: loadingOptions.showDuration || 0
                    });
                }


                chart.loadingShown = true;
                setLoadingSize();
            },

            /**
             * Hide the loading layer
             */
            hideLoading: function() {
                var options = this.options,
                    loadingDiv = this.loadingDiv;

                if (loadingDiv) {
                    loadingDiv.className = 'highcharts-loading highcharts-loading-hidden';

                    animate(loadingDiv, {
                        opacity: 0
                    }, {
                        duration: options.loading.hideDuration || 100,
                        complete: function() {
                            css(loadingDiv, {
                                display: 'none'
                            });
                        }
                    });

                }
                this.loadingShown = false;
            },

            /** 
             * These properties cause isDirtyBox to be set to true when updating. Can be extended from plugins.
             */
            propsRequireDirtyBox: ['backgroundColor', 'borderColor', 'borderWidth', 'margin', 'marginTop', 'marginRight',
                'marginBottom', 'marginLeft', 'spacing', 'spacingTop', 'spacingRight', 'spacingBottom', 'spacingLeft',
                'borderRadius', 'plotBackgroundColor', 'plotBackgroundImage', 'plotBorderColor', 'plotBorderWidth',
                'plotShadow', 'shadow'
            ],

            /** 
             * These properties cause all series to be updated when updating. Can be
             * extended from plugins.
             */
            propsRequireUpdateSeries: ['chart.inverted', 'chart.polar',
                'chart.ignoreHiddenSeries', 'chart.type', 'colors', 'plotOptions'
            ],

            /**
             * Chart.update function that takes the whole options stucture.
             */
            update: function(options, redraw) {
                var key,
                    adders = {
                        credits: 'addCredits',
                        title: 'setTitle',
                        subtitle: 'setSubtitle'
                    },
                    optionsChart = options.chart,
                    updateAllAxes,
                    updateAllSeries,
                    newWidth,
                    newHeight;

                // If the top-level chart option is present, some special updates are required		
                if (optionsChart) {
                    merge(true, this.options.chart, optionsChart);

                    // Setter function
                    if ('className' in optionsChart) {
                        this.setClassName(optionsChart.className);
                    }

                    if ('inverted' in optionsChart || 'polar' in optionsChart) {
                        this.propFromSeries(); // Parses options.chart.inverted and options.chart.polar together with the available series
                        updateAllAxes = true;
                    }

                    for (key in optionsChart) {
                        if (optionsChart.hasOwnProperty(key)) {
                            if (inArray('chart.' + key, this.propsRequireUpdateSeries) !== -1) {
                                updateAllSeries = true;
                            }
                            // Only dirty box
                            if (inArray(key, this.propsRequireDirtyBox) !== -1) {
                                this.isDirtyBox = true;
                            }

                        }
                    }


                    if ('style' in optionsChart) {
                        this.renderer.setStyle(optionsChart.style);
                    }

                }

                // Some option stuctures correspond one-to-one to chart objects that have
                // update methods, for example
                // options.credits => chart.credits
                // options.legend => chart.legend
                // options.title => chart.title
                // options.tooltip => chart.tooltip
                // options.subtitle => chart.subtitle
                // options.navigator => chart.navigator
                // options.scrollbar => chart.scrollbar
                for (key in options) {
                    if (this[key] && typeof this[key].update === 'function') {
                        this[key].update(options[key], false);

                        // If a one-to-one object does not exist, look for an adder function
                    } else if (typeof this[adders[key]] === 'function') {
                        this[adders[key]](options[key]);
                    }

                    if (key !== 'chart' && inArray(key, this.propsRequireUpdateSeries) !== -1) {
                        updateAllSeries = true;
                    }
                }


                if (options.colors) {
                    this.options.colors = options.colors;
                }


                if (options.plotOptions) {
                    merge(true, this.options.plotOptions, options.plotOptions);
                }

                // Setters for collections. For axes and series, each item is referred by an id. If the 
                // id is not found, it defaults to the first item in the collection, so setting series
                // without an id, will update the first series in the chart.
                each(['xAxis', 'yAxis', 'series'], function(coll) {
                    if (options[coll]) {
                        each(splat(options[coll]), function(newOptions) {
                            var item = (defined(newOptions.id) && this.get(newOptions.id)) || this[coll][0];
                            if (item && item.coll === coll) {
                                item.update(newOptions, false);
                            }
                        }, this);
                    }
                }, this);

                if (updateAllAxes) {
                    each(this.axes, function(axis) {
                        axis.update({}, false);
                    });
                }

                // Certain options require the whole series structure to be thrown away
                // and rebuilt
                if (updateAllSeries) {
                    each(this.series, function(series) {
                        series.update({}, false);
                    });
                }

                // For loading, just update the options, do not redraw
                if (options.loading) {
                    merge(true, this.options.loading, options.loading);
                }

                // Update size. Redraw is forced.
                newWidth = optionsChart && optionsChart.width;
                newHeight = optionsChart && optionsChart.height;
                if ((isNumber(newWidth) && newWidth !== this.chartWidth) ||
                    (isNumber(newHeight) && newHeight !== this.chartHeight)) {
                    this.setSize(newWidth, newHeight);
                } else if (pick(redraw, true)) {
                    this.redraw();
                }
            },

            /**
             * Setter function to allow use from chart.update
             */
            setSubtitle: function(options) {
                this.setTitle(undefined, options);
            }


        });

        // extend the Point prototype for dynamic methods
        extend(Point.prototype, /** @lends Point.prototype */ {
            /**
             * Point.update with new options (typically x/y data) and optionally redraw the series.
             *
             * @param {Object} options Point options as defined in the series.data array
             * @param {Boolean} redraw Whether to redraw the chart or wait for an explicit call
             * @param {Boolean|Object} animation Whether to apply animation, and optionally animation
             *    configuration
             */
            update: function(options, redraw, animation, runEvent) {
                var point = this,
                    series = point.series,
                    graphic = point.graphic,
                    i,
                    chart = series.chart,
                    seriesOptions = series.options;

                redraw = pick(redraw, true);

                function update() {

                    point.applyOptions(options);

                    // Update visuals
                    if (point.y === null && graphic) { // #4146
                        point.graphic = graphic.destroy();
                    }
                    if (isObject(options, true)) {
                        // Destroy so we can get new elements
                        if (graphic && graphic.element) {
                            if (options && options.marker && options.marker.symbol) {
                                point.graphic = graphic.destroy();
                            }
                        }
                        if (options && options.dataLabels && point.dataLabel) { // #2468
                            point.dataLabel = point.dataLabel.destroy();
                        }
                    }

                    // record changes in the parallel arrays
                    i = point.index;
                    series.updateParallelArrays(point, i);

                    // Record the options to options.data. If there is an object from before,
                    // use point options, otherwise use raw options. (#4701)
                    seriesOptions.data[i] = isObject(seriesOptions.data[i], true) ? point.options : options;

                    // redraw
                    series.isDirty = series.isDirtyData = true;
                    if (!series.fixedBox && series.hasCartesianSeries) { // #1906, #2320
                        chart.isDirtyBox = true;
                    }

                    if (seriesOptions.legendType === 'point') { // #1831, #1885
                        chart.isDirtyLegend = true;
                    }
                    if (redraw) {
                        chart.redraw(animation);
                    }
                }

                // Fire the event with a default handler of doing the update
                if (runEvent === false) { // When called from setData
                    update();
                } else {
                    point.firePointEvent('update', {
                        options: options
                    }, update);
                }
            },

            /**
             * Remove a point and optionally redraw the series and if necessary the axes
             * @param {Boolean} redraw Whether to redraw the chart or wait for an explicit call
             * @param {Boolean|Object} animation Whether to apply animation, and optionally animation
             *    configuration
             */
            remove: function(redraw, animation) {
                this.series.removePoint(inArray(this, this.series.data), redraw, animation);
            }
        });

        // Extend the series prototype for dynamic methods
        extend(Series.prototype, /** @lends Series.prototype */ {
            /**
             * Add a point dynamically after chart load time
             * @param {Object} options Point options as given in series.data
             * @param {Boolean} redraw Whether to redraw the chart or wait for an explicit call
             * @param {Boolean} shift If shift is true, a point is shifted off the start
             *    of the series as one is appended to the end.
             * @param {Boolean|AnimationOptions} animation Whether to apply animation, and optionally animation
             *    configuration
             */
            addPoint: function(options, redraw, shift, animation) {
                var series = this,
                    seriesOptions = series.options,
                    data = series.data,
                    chart = series.chart,
                    names = series.xAxis && series.xAxis.names,
                    dataOptions = seriesOptions.data,
                    point,
                    isInTheMiddle,
                    xData = series.xData,
                    i,
                    x;

                // Optional redraw, defaults to true
                redraw = pick(redraw, true);

                // Get options and push the point to xData, yData and series.options. In series.generatePoints
                // the Point instance will be created on demand and pushed to the series.data array.
                point = {
                    series: series
                };
                series.pointClass.prototype.applyOptions.apply(point, [options]);
                x = point.x;

                // Get the insertion point
                i = xData.length;
                if (series.requireSorting && x < xData[i - 1]) {
                    isInTheMiddle = true;
                    while (i && xData[i - 1] > x) {
                        i--;
                    }
                }

                series.updateParallelArrays(point, 'splice', i, 0, 0); // insert undefined item
                series.updateParallelArrays(point, i); // update it

                if (names && point.name) {
                    names[x] = point.name;
                }
                dataOptions.splice(i, 0, options);

                if (isInTheMiddle) {
                    series.data.splice(i, 0, null);
                    series.processData();
                }

                // Generate points to be added to the legend (#1329)
                if (seriesOptions.legendType === 'point') {
                    series.generatePoints();
                }

                // Shift the first point off the parallel arrays
                if (shift) {
                    if (data[0] && data[0].remove) {
                        data[0].remove(false);
                    } else {
                        data.shift();
                        series.updateParallelArrays(point, 'shift');

                        dataOptions.shift();
                    }
                }

                // redraw
                series.isDirty = true;
                series.isDirtyData = true;

                if (redraw) {

                    chart.redraw(animation); // Animation is set anyway on redraw, #5665
                }
            },

            /**
             * Remove a point (rendered or not), by index
             */
            removePoint: function(i, redraw, animation) {

                var series = this,
                    data = series.data,
                    point = data[i],
                    points = series.points,
                    chart = series.chart,
                    remove = function() {

                        if (points && points.length === data.length) { // #4935
                            points.splice(i, 1);
                        }
                        data.splice(i, 1);
                        series.options.data.splice(i, 1);
                        series.updateParallelArrays(point || {
                            series: series
                        }, 'splice', i, 1);

                        if (point) {
                            point.destroy();
                        }

                        // redraw
                        series.isDirty = true;
                        series.isDirtyData = true;
                        if (redraw) {
                            chart.redraw();
                        }
                    };

                setAnimation(animation, chart);
                redraw = pick(redraw, true);

                // Fire the event with a default handler of removing the point
                if (point) {
                    point.firePointEvent('remove', null, remove);
                } else {
                    remove();
                }
            },

            /**
             * Remove a series and optionally redraw the chart
             *
             * @param {Boolean} redraw Whether to redraw the chart or wait for an explicit call
             * @param {Boolean|Object} animation Whether to apply animation, and optionally animation
             *    configuration
             */
            remove: function(redraw, animation, withEvent) {
                var series = this,
                    chart = series.chart;

                function remove() {

                    // Destroy elements
                    series.destroy();

                    // Redraw
                    chart.isDirtyLegend = chart.isDirtyBox = true;
                    chart.linkSeries();

                    if (pick(redraw, true)) {
                        chart.redraw(animation);
                    }
                }

                // Fire the event with a default handler of removing the point
                if (withEvent !== false) {
                    fireEvent(series, 'remove', null, remove);
                } else {
                    remove();
                }
            },

            /**
             * Series.update with a new set of options
             */
            update: function(newOptions, redraw) {
                var series = this,
                    chart = this.chart,
                    // must use user options when changing type because this.options is merged
                    // in with type specific plotOptions
                    oldOptions = this.userOptions,
                    oldType = this.type,
                    newType = newOptions.type || oldOptions.type || chart.options.chart.type,
                    proto = seriesTypes[oldType].prototype,
                    preserve = ['group', 'markerGroup', 'dataLabelsGroup'],
                    n;

                // If we're changing type or zIndex, create new groups (#3380, #3404)
                if ((newType && newType !== oldType) || newOptions.zIndex !== undefined) {
                    preserve.length = 0;
                }

                // Make sure groups are not destroyed (#3094)
                each(preserve, function(prop) {
                    preserve[prop] = series[prop];
                    delete series[prop];
                });

                // Do the merge, with some forced options
                newOptions = merge(oldOptions, {
                    animation: false,
                    index: this.index,
                    pointStart: this.xData[0] // when updating after addPoint
                }, {
                    data: this.options.data
                }, newOptions);

                // Destroy the series and delete all properties. Reinsert all methods
                // and properties from the new type prototype (#2270, #3719)
                this.remove(false, null, false);
                for (n in proto) {
                    this[n] = undefined;
                }
                extend(this, seriesTypes[newType || oldType].prototype);

                // Re-register groups (#3094)
                each(preserve, function(prop) {
                    series[prop] = preserve[prop];
                });

                this.init(chart, newOptions);
                chart.linkSeries(); // Links are lost in this.remove (#3028)
                if (pick(redraw, true)) {
                    chart.redraw(false);
                }
            }
        });

        // Extend the Axis.prototype for dynamic methods
        extend(Axis.prototype, /** @lends Axis.prototype */ {

            /**
             * Axis.update with a new options structure
             */
            update: function(newOptions, redraw) {
                var chart = this.chart;

                newOptions = chart.options[this.coll][this.options.index] = merge(this.userOptions, newOptions);

                this.destroy(true);

                this.init(chart, extend(newOptions, {
                    events: undefined
                }));

                chart.isDirtyBox = true;
                if (pick(redraw, true)) {
                    chart.redraw();
                }
            },

            /**
             * Remove the axis from the chart
             */
            remove: function(redraw) {
                var chart = this.chart,
                    key = this.coll, // xAxis or yAxis
                    axisSeries = this.series,
                    i = axisSeries.length;

                // Remove associated series (#2687)
                while (i--) {
                    if (axisSeries[i]) {
                        axisSeries[i].remove(false);
                    }
                }

                // Remove the axis
                erase(chart.axes, this);
                erase(chart[key], this);
                chart.options[key].splice(this.options.index, 1);
                each(chart[key], function(axis, i) { // Re-index, #1706
                    axis.options.index = i;
                });
                this.destroy();
                chart.isDirtyBox = true;

                if (pick(redraw, true)) {
                    chart.redraw();
                }
            },

            /**
             * Update the axis title by options
             */
            setTitle: function(newTitleOptions, redraw) {
                this.update({
                    title: newTitleOptions
                }, redraw);
            },

            /**
             * Set new axis categories and optionally redraw
             * @param {Array} categories
             * @param {Boolean} redraw
             */
            setCategories: function(categories, redraw) {
                this.update({
                    categories: categories
                }, redraw);
            }

        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var color = H.color,
            each = H.each,
            LegendSymbolMixin = H.LegendSymbolMixin,
            map = H.map,
            pick = H.pick,
            Series = H.Series,
            seriesType = H.seriesType;

        /**
         * Area series type.
         * @constructor seriesTypes.area
         * @extends {Series}
         */
        seriesType('area', 'line', {
            softThreshold: false,
            threshold: 0
                // trackByArea: false,
                // lineColor: null, // overrides color, but lets fillColor be unaltered
                // fillOpacity: 0.75,
                // fillColor: null
        }, /** @lends seriesTypes.area.prototype */ {
            singleStacks: false,
            /** 
             * Return an array of stacked points, where null and missing points are replaced by 
             * dummy points in order for gaps to be drawn correctly in stacks.
             */
            getStackPoints: function() {
                var series = this,
                    segment = [],
                    keys = [],
                    xAxis = this.xAxis,
                    yAxis = this.yAxis,
                    stack = yAxis.stacks[this.stackKey],
                    pointMap = {},
                    points = this.points,
                    seriesIndex = series.index,
                    yAxisSeries = yAxis.series,
                    seriesLength = yAxisSeries.length,
                    visibleSeries,
                    upOrDown = pick(yAxis.options.reversedStacks, true) ? 1 : -1,
                    i,
                    x;

                if (this.options.stacking) {
                    // Create a map where we can quickly look up the points by their X value.
                    for (i = 0; i < points.length; i++) {
                        pointMap[points[i].x] = points[i];
                    }

                    // Sort the keys (#1651)
                    for (x in stack) {
                        if (stack[x].total !== null) { // nulled after switching between grouping and not (#1651, #2336)
                            keys.push(x);
                        }
                    }
                    keys.sort(function(a, b) {
                        return a - b;
                    });

                    visibleSeries = map(yAxisSeries, function() {
                        return this.visible;
                    });

                    each(keys, function(x, idx) {
                        var y = 0,
                            stackPoint,
                            stackedValues;

                        if (pointMap[x] && !pointMap[x].isNull) {
                            segment.push(pointMap[x]);

                            // Find left and right cliff. -1 goes left, 1 goes right.
                            each([-1, 1], function(direction) {
                                var nullName = direction === 1 ? 'rightNull' : 'leftNull',
                                    cliffName = direction === 1 ? 'rightCliff' : 'leftCliff',
                                    cliff = 0,
                                    otherStack = stack[keys[idx + direction]];

                                // If there is a stack next to this one, to the left or to the right...
                                if (otherStack) {
                                    i = seriesIndex;
                                    while (i >= 0 && i < seriesLength) { // Can go either up or down, depending on reversedStacks
                                        stackPoint = otherStack.points[i];
                                        if (!stackPoint) {
                                            // If the next point in this series is missing, mark the point
                                            // with point.leftNull or point.rightNull = true.
                                            if (i === seriesIndex) {
                                                pointMap[x][nullName] = true;

                                                // If there are missing points in the next stack in any of the 
                                                // series below this one, we need to substract the missing values
                                                // and add a hiatus to the left or right.
                                            } else if (visibleSeries[i]) {
                                                stackedValues = stack[x].points[i];
                                                if (stackedValues) {
                                                    cliff -= stackedValues[1] - stackedValues[0];
                                                }
                                            }
                                        }
                                        // When reversedStacks is true, loop up, else loop down
                                        i += upOrDown;
                                    }
                                }
                                pointMap[x][cliffName] = cliff;
                            });


                            // There is no point for this X value in this series, so we 
                            // insert a dummy point in order for the areas to be drawn
                            // correctly.
                        } else {

                            // Loop down the stack to find the series below this one that has
                            // a value (#1991)
                            i = seriesIndex;
                            while (i >= 0 && i < seriesLength) {
                                stackPoint = stack[x].points[i];
                                if (stackPoint) {
                                    y = stackPoint[1];
                                    break;
                                }
                                // When reversedStacks is true, loop up, else loop down
                                i += upOrDown;
                            }

                            y = yAxis.toPixels(y, true);
                            segment.push({
                                isNull: true,
                                plotX: xAxis.toPixels(x, true),
                                plotY: y,
                                yBottom: y
                            });
                        }
                    });

                }

                return segment;
            },

            getGraphPath: function(points) {
                var getGraphPath = Series.prototype.getGraphPath,
                    graphPath,
                    options = this.options,
                    stacking = options.stacking,
                    yAxis = this.yAxis,
                    topPath,
                    //topPoints = [],
                    bottomPath,
                    bottomPoints = [],
                    graphPoints = [],
                    seriesIndex = this.index,
                    i,
                    areaPath,
                    plotX,
                    stacks = yAxis.stacks[this.stackKey],
                    threshold = options.threshold,
                    translatedThreshold = yAxis.getThreshold(options.threshold),
                    isNull,
                    yBottom,
                    connectNulls = options.connectNulls || stacking === 'percent',
                    /**
                     * To display null points in underlying stacked series, this series graph must be 
                     * broken, and the area also fall down to fill the gap left by the null point. #2069
                     */
                    addDummyPoints = function(i, otherI, side) {
                        var point = points[i],
                            stackedValues = stacking && stacks[point.x].points[seriesIndex],
                            nullVal = point[side + 'Null'] || 0,
                            cliffVal = point[side + 'Cliff'] || 0,
                            top,
                            bottom,
                            isNull = true;

                        if (cliffVal || nullVal) {

                            top = (nullVal ? stackedValues[0] : stackedValues[1]) + cliffVal;
                            bottom = stackedValues[0] + cliffVal;
                            isNull = !!nullVal;

                        } else if (!stacking && points[otherI] && points[otherI].isNull) {
                            top = bottom = threshold;
                        }

                        // Add to the top and bottom line of the area
                        if (top !== undefined) {
                            graphPoints.push({
                                plotX: plotX,
                                plotY: top === null ? translatedThreshold : yAxis.getThreshold(top),
                                isNull: isNull
                            });
                            bottomPoints.push({
                                plotX: plotX,
                                plotY: bottom === null ? translatedThreshold : yAxis.getThreshold(bottom),
                                doCurve: false // #1041, gaps in areaspline areas
                            });
                        }
                    };

                // Find what points to use
                points = points || this.points;

                // Fill in missing points
                if (stacking) {
                    points = this.getStackPoints();
                }

                for (i = 0; i < points.length; i++) {
                    isNull = points[i].isNull;
                    plotX = pick(points[i].rectPlotX, points[i].plotX);
                    yBottom = pick(points[i].yBottom, translatedThreshold);

                    if (!isNull || connectNulls) {

                        if (!connectNulls) {
                            addDummyPoints(i, i - 1, 'left');
                        }

                        if (!(isNull && !stacking && connectNulls)) { // Skip null point when stacking is false and connectNulls true
                            graphPoints.push(points[i]);
                            bottomPoints.push({
                                x: i,
                                plotX: plotX,
                                plotY: yBottom
                            });
                        }

                        if (!connectNulls) {
                            addDummyPoints(i, i + 1, 'right');
                        }
                    }
                }

                topPath = getGraphPath.call(this, graphPoints, true, true);

                bottomPoints.reversed = true;
                bottomPath = getGraphPath.call(this, bottomPoints, true, true);
                if (bottomPath.length) {
                    bottomPath[0] = 'L';
                }

                areaPath = topPath.concat(bottomPath);
                graphPath = getGraphPath.call(this, graphPoints, false, connectNulls); // TODO: don't set leftCliff and rightCliff when connectNulls?

                areaPath.xMap = topPath.xMap;
                this.areaPath = areaPath;
                return graphPath;
            },

            /**
             * Draw the graph and the underlying area. This method calls the Series base
             * function and adds the area. The areaPath is calculated in the getSegmentPath
             * method called from Series.prototype.drawGraph.
             */
            drawGraph: function() {

                // Define or reset areaPath
                this.areaPath = [];

                // Call the base method
                Series.prototype.drawGraph.apply(this);

                // Define local variables
                var series = this,
                    areaPath = this.areaPath,
                    options = this.options,
                    zones = this.zones,
                    props = [
                        [
                            'area',
                            'highcharts-area',

                            this.color,
                            options.fillColor

                        ]
                    ]; // area name, main color, fill color

                each(zones, function(zone, i) {
                    props.push([
                        'zone-area-' + i,
                        'highcharts-area highcharts-zone-area-' + i + ' ' + zone.className,

                        zone.color || series.color,
                        zone.fillColor || options.fillColor

                    ]);
                });

                each(props, function(prop) {
                    var areaKey = prop[0],
                        area = series[areaKey];

                    // Create or update the area
                    if (area) { // update
                        area.endX = areaPath.xMap;
                        area.animate({
                            d: areaPath
                        });

                    } else { // create
                        area = series[areaKey] = series.chart.renderer.path(areaPath)
                            .addClass(prop[1])
                            .attr({

                                fill: pick(
                                    prop[3],
                                    color(prop[2]).setOpacity(pick(options.fillOpacity, 0.75)).get()
                                ),

                                zIndex: 0 // #1069
                            }).add(series.group);
                        area.isArea = true;
                    }
                    area.startX = areaPath.xMap;
                    area.shiftUnit = options.step ? 2 : 1;
                });
            },

            drawLegendSymbol: LegendSymbolMixin.drawRectangle
        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var pick = H.pick,
            seriesType = H.seriesType;

        /**
         * Spline series type.
         * @constructor seriesTypes.spline
         * @extends {Series}
         */
        seriesType('spline', 'line', {}, /** @lends seriesTypes.spline.prototype */ {
            /**
             * Get the spline segment from a given point's previous neighbour to the given point
             */
            getPointSpline: function(points, point, i) {
                var smoothing = 1.5, // 1 means control points midway between points, 2 means 1/3 from the point, 3 is 1/4 etc
                    denom = smoothing + 1,
                    plotX = point.plotX,
                    plotY = point.plotY,
                    lastPoint = points[i - 1],
                    nextPoint = points[i + 1],
                    leftContX,
                    leftContY,
                    rightContX,
                    rightContY,
                    ret;

                function doCurve(otherPoint) {
                    return otherPoint && !otherPoint.isNull && otherPoint.doCurve !== false;
                }

                // Find control points
                if (doCurve(lastPoint) && doCurve(nextPoint)) {
                    var lastX = lastPoint.plotX,
                        lastY = lastPoint.plotY,
                        nextX = nextPoint.plotX,
                        nextY = nextPoint.plotY,
                        correction = 0;

                    leftContX = (smoothing * plotX + lastX) / denom;
                    leftContY = (smoothing * plotY + lastY) / denom;
                    rightContX = (smoothing * plotX + nextX) / denom;
                    rightContY = (smoothing * plotY + nextY) / denom;

                    // Have the two control points make a straight line through main point
                    if (rightContX !== leftContX) { // #5016, division by zero
                        correction = ((rightContY - leftContY) * (rightContX - plotX)) /
                            (rightContX - leftContX) + plotY - rightContY;
                    }

                    leftContY += correction;
                    rightContY += correction;

                    // to prevent false extremes, check that control points are between
                    // neighbouring points' y values
                    if (leftContY > lastY && leftContY > plotY) {
                        leftContY = Math.max(lastY, plotY);
                        rightContY = 2 * plotY - leftContY; // mirror of left control point
                    } else if (leftContY < lastY && leftContY < plotY) {
                        leftContY = Math.min(lastY, plotY);
                        rightContY = 2 * plotY - leftContY;
                    }
                    if (rightContY > nextY && rightContY > plotY) {
                        rightContY = Math.max(nextY, plotY);
                        leftContY = 2 * plotY - rightContY;
                    } else if (rightContY < nextY && rightContY < plotY) {
                        rightContY = Math.min(nextY, plotY);
                        leftContY = 2 * plotY - rightContY;
                    }

                    // record for drawing in next point
                    point.rightContX = rightContX;
                    point.rightContY = rightContY;


                }

                // Visualize control points for debugging
                /*
                if (leftContX) {
                	this.chart.renderer.circle(leftContX + this.chart.plotLeft, leftContY + this.chart.plotTop, 2)
                		.attr({
                			stroke: 'red',
                			'stroke-width': 2,
                			fill: 'none',
                			zIndex: 9
                		})
                		.add();
                	this.chart.renderer.path(['M', leftContX + this.chart.plotLeft, leftContY + this.chart.plotTop,
                		'L', plotX + this.chart.plotLeft, plotY + this.chart.plotTop])
                		.attr({
                			stroke: 'red',
                			'stroke-width': 2,
                			zIndex: 9
                		})
                		.add();
                }
                if (rightContX) {
                	this.chart.renderer.circle(rightContX + this.chart.plotLeft, rightContY + this.chart.plotTop, 2)
                		.attr({
                			stroke: 'green',
                			'stroke-width': 2,
                			fill: 'none',
                			zIndex: 9
                		})
                		.add();
                	this.chart.renderer.path(['M', rightContX + this.chart.plotLeft, rightContY + this.chart.plotTop,
                		'L', plotX + this.chart.plotLeft, plotY + this.chart.plotTop])
                		.attr({
                			stroke: 'green',
                			'stroke-width': 2,
                			zIndex: 9
                		})
                		.add();
                }
                // */
                ret = [
                    'C',
                    pick(lastPoint.rightContX, lastPoint.plotX),
                    pick(lastPoint.rightContY, lastPoint.plotY),
                    pick(leftContX, plotX),
                    pick(leftContY, plotY),
                    plotX,
                    plotY
                ];
                lastPoint.rightContX = lastPoint.rightContY = null; // reset for updating series later
                return ret;
            }
        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var areaProto = H.seriesTypes.area.prototype,
            defaultPlotOptions = H.defaultPlotOptions,
            LegendSymbolMixin = H.LegendSymbolMixin,
            seriesType = H.seriesType;
        /**
         * AreaSplineSeries object
         */
        seriesType('areaspline', 'spline', defaultPlotOptions.area, {
            getStackPoints: areaProto.getStackPoints,
            getGraphPath: areaProto.getGraphPath,
            setStackCliffs: areaProto.setStackCliffs,
            drawGraph: areaProto.drawGraph,
            drawLegendSymbol: LegendSymbolMixin.drawRectangle
        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var animObject = H.animObject,
            color = H.color,
            each = H.each,
            extend = H.extend,
            isNumber = H.isNumber,
            LegendSymbolMixin = H.LegendSymbolMixin,
            merge = H.merge,
            noop = H.noop,
            pick = H.pick,
            Series = H.Series,
            seriesType = H.seriesType,
            svg = H.svg;
        /**
         * The column series type.
         *
         * @constructor seriesTypes.column
         * @augments Series
         */
        seriesType('column', 'line', {
            borderRadius: 0,
            //colorByPoint: undefined,
            groupPadding: 0.2,
            //grouping: true,
            marker: null, // point options are specified in the base options
            pointPadding: 0.1,
            //pointWidth: null,
            minPointLength: 0,
            cropThreshold: 50, // when there are more points, they will not animate out of the chart on xAxis.setExtremes
            pointRange: null, // null means auto, meaning 1 in a categorized axis and least distance between points if not categories
            states: {
                hover: {
                    halo: false,

                    brightness: 0.1,
                    shadow: false

                },

                select: {
                    color: '#cccccc',
                    borderColor: '#000000',
                    shadow: false
                }

            },
            dataLabels: {
                align: null, // auto
                verticalAlign: null, // auto
                y: null
            },
            softThreshold: false,
            startFromThreshold: true, // false doesn't work well: http://jsfiddle.net/highcharts/hz8fopan/14/
            stickyTracking: false,
            tooltip: {
                distance: 6
            },
            threshold: 0,

            borderColor: '#ffffff'
                // borderWidth: 1


        }, /** @lends seriesTypes.column.prototype */ {
            cropShoulder: 0,
            directTouch: true, // When tooltip is not shared, this series (and derivatives) requires direct touch/hover. KD-tree does not apply.
            trackerGroups: ['group', 'dataLabelsGroup'],
            negStacks: true, // use separate negative stacks, unlike area stacks where a negative
            // point is substracted from previous (#1910)

            /**
             * Initialize the series. Extends the basic Series.init method by
             * marking other series of the same type as dirty.
             *
             * @function #init
             * @memberOf seriesTypes.column
             * @returns {void}
             */
            init: function() {
                Series.prototype.init.apply(this, arguments);

                var series = this,
                    chart = series.chart;

                // if the series is added dynamically, force redraw of other
                // series affected by a new column
                if (chart.hasRendered) {
                    each(chart.series, function(otherSeries) {
                        if (otherSeries.type === series.type) {
                            otherSeries.isDirty = true;
                        }
                    });
                }
            },

            /**
             * Return the width and x offset of the columns adjusted for grouping, groupPadding, pointPadding,
             * pointWidth etc.
             */
            getColumnMetrics: function() {

                var series = this,
                    options = series.options,
                    xAxis = series.xAxis,
                    yAxis = series.yAxis,
                    reversedXAxis = xAxis.reversed,
                    stackKey,
                    stackGroups = {},
                    columnCount = 0;

                // Get the total number of column type series.
                // This is called on every series. Consider moving this logic to a
                // chart.orderStacks() function and call it on init, addSeries and removeSeries
                if (options.grouping === false) {
                    columnCount = 1;
                } else {
                    each(series.chart.series, function(otherSeries) {
                        var otherOptions = otherSeries.options,
                            otherYAxis = otherSeries.yAxis,
                            columnIndex;
                        if (otherSeries.type === series.type && otherSeries.visible &&
                            yAxis.len === otherYAxis.len && yAxis.pos === otherYAxis.pos) { // #642, #2086
                            if (otherOptions.stacking) {
                                stackKey = otherSeries.stackKey;
                                if (stackGroups[stackKey] === undefined) {
                                    stackGroups[stackKey] = columnCount++;
                                }
                                columnIndex = stackGroups[stackKey];
                            } else if (otherOptions.grouping !== false) { // #1162
                                columnIndex = columnCount++;
                            }
                            otherSeries.columnIndex = columnIndex;
                        }
                    });
                }

                var categoryWidth = Math.min(
                        Math.abs(xAxis.transA) * (xAxis.ordinalSlope || options.pointRange || xAxis.closestPointRange || xAxis.tickInterval || 1), // #2610
                        xAxis.len // #1535
                    ),
                    groupPadding = categoryWidth * options.groupPadding,
                    groupWidth = categoryWidth - 2 * groupPadding,
                    pointOffsetWidth = groupWidth / columnCount,
                    pointWidth = Math.min(
                        options.maxPointWidth || xAxis.len,
                        pick(options.pointWidth, pointOffsetWidth * (1 - 2 * options.pointPadding))
                    ),
                    pointPadding = (pointOffsetWidth - pointWidth) / 2,
                    colIndex = (series.columnIndex || 0) + (reversedXAxis ? 1 : 0), // #1251, #3737
                    pointXOffset = pointPadding + (groupPadding + colIndex *
                        pointOffsetWidth - (categoryWidth / 2)) *
                    (reversedXAxis ? -1 : 1);

                // Save it for reading in linked series (Error bars particularly)
                series.columnMetrics = {
                    width: pointWidth,
                    offset: pointXOffset
                };
                return series.columnMetrics;

            },

            /**
             * Make the columns crisp. The edges are rounded to the nearest full pixel.
             */
            crispCol: function(x, y, w, h) {
                var chart = this.chart,
                    borderWidth = this.borderWidth,
                    xCrisp = -(borderWidth % 2 ? 0.5 : 0),
                    yCrisp = borderWidth % 2 ? 0.5 : 1,
                    right,
                    bottom,
                    fromTop;

                if (chart.inverted && chart.renderer.isVML) {
                    yCrisp += 1;
                }

                // Horizontal. We need to first compute the exact right edge, then round it
                // and compute the width from there.
                right = Math.round(x + w) + xCrisp;
                x = Math.round(x) + xCrisp;
                w = right - x;

                // Vertical
                bottom = Math.round(y + h) + yCrisp;
                fromTop = Math.abs(y) <= 0.5 && bottom > 0.5; // #4504, #4656
                y = Math.round(y) + yCrisp;
                h = bottom - y;

                // Top edges are exceptions
                if (fromTop && h) { // #5146
                    y -= 1;
                    h += 1;
                }

                return {
                    x: x,
                    y: y,
                    width: w,
                    height: h
                };
            },

            /**
             * Translate each point to the plot area coordinate system and find shape positions
             */
            translate: function() {
                var series = this,
                    chart = series.chart,
                    options = series.options,
                    dense = series.dense = series.closestPointRange * series.xAxis.transA < 2,
                    borderWidth = series.borderWidth = pick(
                        options.borderWidth,
                        dense ? 0 : 1 // #3635
                    ),
                    yAxis = series.yAxis,
                    threshold = options.threshold,
                    translatedThreshold = series.translatedThreshold = yAxis.getThreshold(threshold),
                    minPointLength = pick(options.minPointLength, 5),
                    metrics = series.getColumnMetrics(),
                    pointWidth = metrics.width,
                    seriesBarW = series.barW = Math.max(pointWidth, 1 + 2 * borderWidth), // postprocessed for border width
                    pointXOffset = series.pointXOffset = metrics.offset;

                if (chart.inverted) {
                    translatedThreshold -= 0.5; // #3355
                }

                // When the pointPadding is 0, we want the columns to be packed tightly, so we allow individual
                // columns to have individual sizes. When pointPadding is greater, we strive for equal-width
                // columns (#2694).
                if (options.pointPadding) {
                    seriesBarW = Math.ceil(seriesBarW);
                }

                Series.prototype.translate.apply(series);

                // Record the new values
                each(series.points, function(point) {
                    var yBottom = pick(point.yBottom, translatedThreshold),
                        safeDistance = 999 + Math.abs(yBottom),
                        plotY = Math.min(Math.max(-safeDistance, point.plotY), yAxis.len + safeDistance), // Don't draw too far outside plot area (#1303, #2241, #4264)
                        barX = point.plotX + pointXOffset,
                        barW = seriesBarW,
                        barY = Math.min(plotY, yBottom),
                        up,
                        barH = Math.max(plotY, yBottom) - barY;

                    // Handle options.minPointLength
                    if (Math.abs(barH) < minPointLength) {
                        if (minPointLength) {
                            barH = minPointLength;
                            up = (!yAxis.reversed && !point.negative) || (yAxis.reversed && point.negative);
                            barY = Math.abs(barY - translatedThreshold) > minPointLength ? // stacked
                                yBottom - minPointLength : // keep position
                                translatedThreshold - (up ? minPointLength : 0); // #1485, #4051
                        }
                    }

                    // Cache for access in polar
                    point.barX = barX;
                    point.pointWidth = pointWidth;

                    // Fix the tooltip on center of grouped columns (#1216, #424, #3648)
                    point.tooltipPos = chart.inverted ? [yAxis.len + yAxis.pos - chart.plotLeft - plotY, series.xAxis.len - barX - barW / 2, barH] : [barX + barW / 2, plotY + yAxis.pos - chart.plotTop, barH];

                    // Register shape type and arguments to be used in drawPoints
                    point.shapeType = 'rect';
                    point.shapeArgs = series.crispCol.apply(
                        series,
                        point.isNull ? [point.plotX, yAxis.len / 2, 0, 0] : // #3169, drilldown from null must have a position to work from
                        [barX, barY, barW, barH]
                    );
                });

            },

            getSymbol: noop,

            /**
             * Use a solid rectangle like the area series types
             */
            drawLegendSymbol: LegendSymbolMixin.drawRectangle,


            /**
             * Columns have no graph
             */
            drawGraph: function() {
                this.group[this.dense ? 'addClass' : 'removeClass']('highcharts-dense-data');
            },


            /**
             * Get presentational attributes
             */
            pointAttribs: function(point, state) {
                var options = this.options,
                    stateOptions,
                    ret,
                    p2o = this.pointAttrToOptions || {},
                    strokeOption = p2o.stroke || 'borderColor',
                    strokeWidthOption = p2o['stroke-width'] || 'borderWidth',
                    fill = (point && point.color) || this.color,
                    stroke = point[strokeOption] || options[strokeOption] ||
                    this.color || fill, // set to fill when borderColor null
                    dashstyle = options.dashStyle,
                    zone,
                    brightness;

                // Handle zone colors
                if (point && this.zones.length) {
                    zone = point.getZone();
                    fill = (zone && zone.color) || point.options.color || this.color; // When zones are present, don't use point.color (#4267)
                }

                // Select or hover states
                if (state) {
                    stateOptions = options.states[state];
                    brightness = stateOptions.brightness;
                    fill = stateOptions.color ||
                        (brightness !== undefined && color(fill).brighten(stateOptions.brightness).get()) ||
                        fill;
                    stroke = stateOptions[strokeOption] || stroke;
                    dashstyle = stateOptions.dashStyle || dashstyle;
                }

                ret = {
                    'fill': fill,
                    'stroke': stroke,
                    'stroke-width': point[strokeWidthOption] || options[strokeWidthOption] || this[strokeWidthOption] || 0
                };
                if (options.borderRadius) {
                    ret.r = options.borderRadius;
                }

                if (dashstyle) {
                    ret.dashstyle = dashstyle;
                }

                return ret;
            },


            /**
             * Draw the columns. For bars, the series.group is rotated, so the same coordinates
             * apply for columns and bars. This method is inherited by scatter series.
             *
             */
            drawPoints: function() {
                var series = this,
                    chart = this.chart,
                    options = series.options,
                    renderer = chart.renderer,
                    animationLimit = options.animationLimit || 250,
                    shapeArgs;

                // draw the columns
                each(series.points, function(point) {
                    var plotY = point.plotY,
                        graphic = point.graphic;

                    if (isNumber(plotY) && point.y !== null) {
                        shapeArgs = point.shapeArgs;

                        if (graphic) { // update
                            graphic[chart.pointCount < animationLimit ? 'animate' : 'attr'](
                                merge(shapeArgs)
                            );

                        } else {
                            point.graphic = graphic = renderer[point.shapeType](shapeArgs)
                                .attr({
                                    'class': point.getClassName()
                                })
                                .add(point.group || series.group);
                        }


                        // Presentational
                        graphic
                            .attr(series.pointAttribs(point, point.selected && 'select'))
                            .shadow(options.shadow, null, options.stacking && !options.borderRadius);


                    } else if (graphic) {
                        point.graphic = graphic.destroy(); // #1269
                    }
                });
            },

            /**
             * Animate the column heights one by one from zero
             * @param {Boolean} init Whether to initialize the animation or run it
             */
            animate: function(init) {
                var series = this,
                    yAxis = this.yAxis,
                    options = series.options,
                    inverted = this.chart.inverted,
                    attr = {},
                    translatedThreshold;

                if (svg) { // VML is too slow anyway
                    if (init) {
                        attr.scaleY = 0.001;
                        translatedThreshold = Math.min(yAxis.pos + yAxis.len, Math.max(yAxis.pos, yAxis.toPixels(options.threshold)));
                        if (inverted) {
                            attr.translateX = translatedThreshold - yAxis.len;
                        } else {
                            attr.translateY = translatedThreshold;
                        }
                        series.group.attr(attr);

                    } else { // run the animation

                        attr[inverted ? 'translateX' : 'translateY'] = yAxis.pos;
                        series.group.animate(attr, extend(animObject(series.options.animation), {
                            // Do the scale synchronously to ensure smooth updating (#5030)
                            step: function(val, fx) {
                                series.group.attr({
                                    scaleY: Math.max(0.001, fx.pos) // #5250
                                });
                            }
                        }));

                        // delete this function to allow it only once
                        series.animate = null;
                    }
                }
            },

            /**
             * Remove this series from the chart
             */
            remove: function() {
                var series = this,
                    chart = series.chart;

                // column and bar series affects other series of the same type
                // as they are either stacked or grouped
                if (chart.hasRendered) {
                    each(chart.series, function(otherSeries) {
                        if (otherSeries.type === series.type) {
                            otherSeries.isDirty = true;
                        }
                    });
                }

                Series.prototype.remove.apply(series, arguments);
            }
        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';

        var seriesType = H.seriesType;

        /**
         * The Bar series class
         */
        seriesType('bar', 'column', null, {
            inverted: true
        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var Series = H.Series,
            seriesType = H.seriesType;
        /**
         * The scatter series type
         */
        seriesType('scatter', 'line', {
            lineWidth: 0,
            marker: {
                enabled: true // Overrides auto-enabling in line series (#3647)
            },
            tooltip: {
                headerFormat: '<span style="color:{point.color}">\u25CF</span> <span style="font-size: 0.85em"> {series.name}</span><br/>',
                pointFormat: 'x: <b>{point.x}</b><br/>y: <b>{point.y}</b><br/>'
            }

            // Prototype members
        }, {
            sorted: false,
            requireSorting: false,
            noSharedTooltip: true,
            trackerGroups: ['group', 'markerGroup', 'dataLabelsGroup'],
            takeOrdinalPosition: false, // #2342
            kdDimensions: 2,
            drawGraph: function() {
                if (this.options.lineWidth) {
                    Series.prototype.drawGraph.call(this);
                }
            }
        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var pick = H.pick,
            relativeLength = H.relativeLength;

        H.CenteredSeriesMixin = {
            /**
             * Get the center of the pie based on the size and center options relative to the
             * plot area. Borrowed by the polar and gauge series types.
             */
            getCenter: function() {

                var options = this.options,
                    chart = this.chart,
                    slicingRoom = 2 * (options.slicedOffset || 0),
                    handleSlicingRoom,
                    plotWidth = chart.plotWidth - 2 * slicingRoom,
                    plotHeight = chart.plotHeight - 2 * slicingRoom,
                    centerOption = options.center,
                    positions = [pick(centerOption[0], '50%'), pick(centerOption[1], '50%'), options.size || '100%', options.innerSize || 0],
                    smallestSize = Math.min(plotWidth, plotHeight),
                    i,
                    value;

                for (i = 0; i < 4; ++i) {
                    value = positions[i];
                    handleSlicingRoom = i < 2 || (i === 2 && /%$/.test(value));

                    // i == 0: centerX, relative to width
                    // i == 1: centerY, relative to height
                    // i == 2: size, relative to smallestSize
                    // i == 3: innerSize, relative to size
                    positions[i] = relativeLength(value, [plotWidth, plotHeight, smallestSize, positions[2]][i]) +
                        (handleSlicingRoom ? slicingRoom : 0);

                }
                // innerSize cannot be larger than size (#3632)
                if (positions[3] > positions[2]) {
                    positions[3] = positions[2];
                }
                return positions;
            }
        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var addEvent = H.addEvent,
            CenteredSeriesMixin = H.CenteredSeriesMixin,
            defined = H.defined,
            each = H.each,
            extend = H.extend,
            inArray = H.inArray,
            LegendSymbolMixin = H.LegendSymbolMixin,
            noop = H.noop,
            pick = H.pick,
            Point = H.Point,
            Series = H.Series,
            seriesType = H.seriesType,
            seriesTypes = H.seriesTypes,
            setAnimation = H.setAnimation;

        /**
         * The pie series type.
         *
         * @constructor seriesTypes.pie
         * @augments Series
         */
        seriesType('pie', 'line', {
            center: [null, null],
            clip: false,
            colorByPoint: true, // always true for pies
            dataLabels: {
                // align: null,
                // connectorWidth: 1,
                // connectorColor: point.color,
                // connectorPadding: 5,
                distance: 30,
                enabled: true,
                formatter: function() { // #2945
                    return this.y === null ? undefined : this.point.name;
                },
                // softConnector: true,
                x: 0
                    // y: 0
            },
            ignoreHiddenPoint: true,
            //innerSize: 0,
            legendType: 'point',
            marker: null, // point options are specified in the base options
            size: null,
            showInLegend: false,
            slicedOffset: 10,
            stickyTracking: false,
            tooltip: {
                followPointer: true
            },

            borderColor: '#ffffff',
            borderWidth: 1,
            states: {
                hover: {
                    brightness: 0.1,
                    shadow: false
                }
            }


        }, /** @lends seriesTypes.pie.prototype */ {
            isCartesian: false,
            requireSorting: false,
            directTouch: true,
            noSharedTooltip: true,
            trackerGroups: ['group', 'dataLabelsGroup'],
            axisTypes: [],
            pointAttribs: seriesTypes.column.prototype.pointAttribs,
            /**
             * Animate the pies in
             */
            animate: function(init) {
                var series = this,
                    points = series.points,
                    startAngleRad = series.startAngleRad;

                if (!init) {
                    each(points, function(point) {
                        var graphic = point.graphic,
                            args = point.shapeArgs;

                        if (graphic) {
                            // start values
                            graphic.attr({
                                r: point.startR || (series.center[3] / 2), // animate from inner radius (#779)
                                start: startAngleRad,
                                end: startAngleRad
                            });

                            // animate
                            graphic.animate({
                                r: args.r,
                                start: args.start,
                                end: args.end
                            }, series.options.animation);
                        }
                    });

                    // delete this function to allow it only once
                    series.animate = null;
                }
            },

            /**
             * Recompute total chart sum and update percentages of points.
             */
            updateTotals: function() {
                var i,
                    total = 0,
                    points = this.points,
                    len = points.length,
                    point,
                    ignoreHiddenPoint = this.options.ignoreHiddenPoint;

                // Get the total sum
                for (i = 0; i < len; i++) {
                    point = points[i];
                    // Disallow negative values (#1530, #3623, #5322)
                    if (point.y < 0) {
                        point.y = null;
                    }
                    total += (ignoreHiddenPoint && !point.visible) ? 0 : point.y;
                }
                this.total = total;

                // Set each point's properties
                for (i = 0; i < len; i++) {
                    point = points[i];
                    point.percentage = (total > 0 && (point.visible || !ignoreHiddenPoint)) ? point.y / total * 100 : 0;
                    point.total = total;
                }
            },

            /**
             * Extend the generatePoints method by adding total and percentage properties to each point
             */
            generatePoints: function() {
                Series.prototype.generatePoints.call(this);
                this.updateTotals();
            },

            /**
             * Do translation for pie slices
             */
            translate: function(positions) {
                this.generatePoints();

                var series = this,
                    cumulative = 0,
                    precision = 1000, // issue #172
                    options = series.options,
                    slicedOffset = options.slicedOffset,
                    connectorOffset = slicedOffset + (options.borderWidth || 0),
                    start,
                    end,
                    angle,
                    startAngle = options.startAngle || 0,
                    startAngleRad = series.startAngleRad = Math.PI / 180 * (startAngle - 90),
                    endAngleRad = series.endAngleRad = Math.PI / 180 * ((pick(options.endAngle, startAngle + 360)) - 90),
                    circ = endAngleRad - startAngleRad, //2 * Math.PI,
                    points = series.points,
                    radiusX, // the x component of the radius vector for a given point
                    radiusY,
                    labelDistance = options.dataLabels.distance,
                    ignoreHiddenPoint = options.ignoreHiddenPoint,
                    i,
                    len = points.length,
                    point;

                // Get positions - either an integer or a percentage string must be given.
                // If positions are passed as a parameter, we're in a recursive loop for adjusting
                // space for data labels.
                if (!positions) {
                    series.center = positions = series.getCenter();
                }

                // utility for getting the x value from a given y, used for anticollision logic in data labels
                series.getX = function(y, left) {

                    angle = Math.asin(Math.min((y - positions[1]) / (positions[2] / 2 + labelDistance), 1));

                    return positions[0] +
                        (left ? -1 : 1) *
                        (Math.cos(angle) * (positions[2] / 2 + labelDistance));
                };

                // Calculate the geometry for each point
                for (i = 0; i < len; i++) {

                    point = points[i];

                    // set start and end angle
                    start = startAngleRad + (cumulative * circ);
                    if (!ignoreHiddenPoint || point.visible) {
                        cumulative += point.percentage / 100;
                    }
                    end = startAngleRad + (cumulative * circ);

                    // set the shape
                    point.shapeType = 'arc';
                    point.shapeArgs = {
                        x: positions[0],
                        y: positions[1],
                        r: positions[2] / 2,
                        innerR: positions[3] / 2,
                        start: Math.round(start * precision) / precision,
                        end: Math.round(end * precision) / precision
                    };

                    // The angle must stay within -90 and 270 (#2645)
                    angle = (end + start) / 2;
                    if (angle > 1.5 * Math.PI) {
                        angle -= 2 * Math.PI;
                    } else if (angle < -Math.PI / 2) {
                        angle += 2 * Math.PI;
                    }

                    // Center for the sliced out slice
                    point.slicedTranslation = {
                        translateX: Math.round(Math.cos(angle) * slicedOffset),
                        translateY: Math.round(Math.sin(angle) * slicedOffset)
                    };

                    // set the anchor point for tooltips
                    radiusX = Math.cos(angle) * positions[2] / 2;
                    radiusY = Math.sin(angle) * positions[2] / 2;
                    point.tooltipPos = [
                        positions[0] + radiusX * 0.7,
                        positions[1] + radiusY * 0.7
                    ];

                    point.half = angle < -Math.PI / 2 || angle > Math.PI / 2 ? 1 : 0;
                    point.angle = angle;

                    // set the anchor point for data labels
                    connectorOffset = Math.min(connectorOffset, labelDistance / 5); // #1678
                    point.labelPos = [
                        positions[0] + radiusX + Math.cos(angle) * labelDistance, // first break of connector
                        positions[1] + radiusY + Math.sin(angle) * labelDistance, // a/a
                        positions[0] + radiusX + Math.cos(angle) * connectorOffset, // second break, right outside pie
                        positions[1] + radiusY + Math.sin(angle) * connectorOffset, // a/a
                        positions[0] + radiusX, // landing point for connector
                        positions[1] + radiusY, // a/a
                        labelDistance < 0 ? // alignment
                        'center' :
                        point.half ? 'right' : 'left', // alignment
                        angle // center angle
                    ];

                }
            },

            drawGraph: null,

            /**
             * Draw the data points
             */
            drawPoints: function() {
                var series = this,
                    chart = series.chart,
                    renderer = chart.renderer,
                    groupTranslation,
                    //center,
                    graphic,
                    //group,
                    pointAttr,
                    shapeArgs;


                var shadow = series.options.shadow;
                if (shadow && !series.shadowGroup) {
                    series.shadowGroup = renderer.g('shadow')
                        .add(series.group);
                }


                // draw the slices
                each(series.points, function(point) {
                    if (point.y !== null) {
                        graphic = point.graphic;
                        shapeArgs = point.shapeArgs;


                        // if the point is sliced, use special translation, else use plot area traslation
                        groupTranslation = point.sliced ? point.slicedTranslation : {};


                        // Put the shadow behind all points
                        var shadowGroup = point.shadowGroup;
                        if (shadow && !shadowGroup) {
                            shadowGroup = point.shadowGroup = renderer.g('shadow')
                                .add(series.shadowGroup);
                        }

                        if (shadowGroup) {
                            shadowGroup.attr(groupTranslation);
                        }
                        pointAttr = series.pointAttribs(point, point.selected && 'select');


                        // Draw the slice
                        if (graphic) {
                            graphic
                                .setRadialReference(series.center)

                            .attr(pointAttr)

                            .animate(extend(shapeArgs, groupTranslation));
                        } else {

                            point.graphic = graphic = renderer[point.shapeType](shapeArgs)
                                .addClass(point.getClassName())
                                .setRadialReference(series.center)
                                .attr(groupTranslation)
                                .add(series.group);

                            if (!point.visible) {
                                graphic.attr({
                                    visibility: 'hidden'
                                });
                            }


                            graphic
                                .attr(pointAttr)
                                .attr({
                                    'stroke-linejoin': 'round'
                                })
                                .shadow(shadow, shadowGroup);

                        }
                    }
                });

            },


            searchPoint: noop,

            /**
             * Utility for sorting data labels
             */
            sortByAngle: function(points, sign) {
                points.sort(function(a, b) {
                    return a.angle !== undefined && (b.angle - a.angle) * sign;
                });
            },

            /**
             * Use a simple symbol from LegendSymbolMixin
             */
            drawLegendSymbol: LegendSymbolMixin.drawRectangle,

            /**
             * Use the getCenter method from drawLegendSymbol
             */
            getCenter: CenteredSeriesMixin.getCenter,

            /**
             * Pies don't have point marker symbols
             */
            getSymbol: noop


            /**
             * @constructor seriesTypes.pie.prototype.pointClass
             * @extends {Point}
             */
        }, /** @lends seriesTypes.pie.prototype.pointClass.prototype */ {
            /**
             * Initiate the pie slice
             */
            init: function() {

                Point.prototype.init.apply(this, arguments);

                var point = this,
                    toggleSlice;

                point.name = pick(point.name, 'Slice');

                // add event listener for select
                toggleSlice = function(e) {
                    point.slice(e.type === 'select');
                };
                addEvent(point, 'select', toggleSlice);
                addEvent(point, 'unselect', toggleSlice);

                return point;
            },

            /**
             * Toggle the visibility of the pie slice
             * @param {Boolean} vis Whether to show the slice or not. If undefined, the
             *    visibility is toggled
             */
            setVisible: function(vis, redraw) {
                var point = this,
                    series = point.series,
                    chart = series.chart,
                    ignoreHiddenPoint = series.options.ignoreHiddenPoint;

                redraw = pick(redraw, ignoreHiddenPoint);

                if (vis !== point.visible) {

                    // If called without an argument, toggle visibility
                    point.visible = point.options.visible = vis = vis === undefined ? !point.visible : vis;
                    series.options.data[inArray(point, series.data)] = point.options; // update userOptions.data

                    // Show and hide associated elements. This is performed regardless of redraw or not,
                    // because chart.redraw only handles full series.
                    each(['graphic', 'dataLabel', 'connector', 'shadowGroup'], function(key) {
                        if (point[key]) {
                            point[key][vis ? 'show' : 'hide'](true);
                        }
                    });

                    if (point.legendItem) {
                        chart.legend.colorizeItem(point, vis);
                    }

                    // #4170, hide halo after hiding point
                    if (!vis && point.state === 'hover') {
                        point.setState('');
                    }

                    // Handle ignore hidden slices
                    if (ignoreHiddenPoint) {
                        series.isDirty = true;
                    }

                    if (redraw) {
                        chart.redraw();
                    }
                }
            },

            /**
             * Set or toggle whether the slice is cut out from the pie
             * @param {Boolean} sliced When undefined, the slice state is toggled
             * @param {Boolean} redraw Whether to redraw the chart. True by default.
             */
            slice: function(sliced, redraw, animation) {
                var point = this,
                    series = point.series,
                    chart = series.chart,
                    translation;

                setAnimation(animation, chart);

                // redraw is true by default
                redraw = pick(redraw, true);

                // if called without an argument, toggle
                point.sliced = point.options.sliced = sliced = defined(sliced) ? sliced : !point.sliced;
                series.options.data[inArray(point, series.data)] = point.options; // update userOptions.data

                translation = sliced ? point.slicedTranslation : {
                    translateX: 0,
                    translateY: 0
                };

                point.graphic.animate(translation);


                if (point.shadowGroup) {
                    point.shadowGroup.animate(translation);
                }

            },

            haloPath: function(size) {
                var shapeArgs = this.shapeArgs;

                return this.sliced || !this.visible ? [] :
                    this.series.chart.renderer.symbols.arc(
                        shapeArgs.x,
                        shapeArgs.y,
                        shapeArgs.r + size,
                        shapeArgs.r + size, {
                            innerR: this.shapeArgs.r,
                            start: shapeArgs.start,
                            end: shapeArgs.end
                        }
                    );
            }
        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var addEvent = H.addEvent,
            arrayMax = H.arrayMax,
            defined = H.defined,
            each = H.each,
            extend = H.extend,
            format = H.format,
            map = H.map,
            merge = H.merge,
            noop = H.noop,
            pick = H.pick,
            relativeLength = H.relativeLength,
            Series = H.Series,
            seriesTypes = H.seriesTypes,
            stableSort = H.stableSort;


        /**
         * Generatl distribution algorithm for distributing labels of differing size along a
         * confined length in two dimensions. The algorithm takes an array of objects containing
         * a size, a target and a rank. It will place the labels as close as possible to their 
         * targets, skipping the lowest ranked labels if necessary.
         */
        H.distribute = function(boxes, len) {

            var i,
                overlapping = true,
                origBoxes = boxes, // Original array will be altered with added .pos
                restBoxes = [], // The outranked overshoot
                box,
                target,
                total = 0;

            function sortByTarget(a, b) {
                return a.target - b.target;
            }

            // If the total size exceeds the len, remove those boxes with the lowest rank
            i = boxes.length;
            while (i--) {
                total += boxes[i].size;
            }

            // Sort by rank, then slice away overshoot
            if (total > len) {
                stableSort(boxes, function(a, b) {
                    return (b.rank || 0) - (a.rank || 0);
                });
                i = 0;
                total = 0;
                while (total <= len) {
                    total += boxes[i].size;
                    i++;
                }
                restBoxes = boxes.splice(i - 1, boxes.length);
            }

            // Order by target
            stableSort(boxes, sortByTarget);


            // So far we have been mutating the original array. Now
            // create a copy with target arrays
            boxes = map(boxes, function(box) {
                return {
                    size: box.size,
                    targets: [box.target]
                };
            });

            while (overlapping) {
                // Initial positions: target centered in box
                i = boxes.length;
                while (i--) {
                    box = boxes[i];
                    // Composite box, average of targets
                    target = (Math.min.apply(0, box.targets) + Math.max.apply(0, box.targets)) / 2;
                    box.pos = Math.min(Math.max(0, target - box.size / 2), len - box.size);
                }

                // Detect overlap and join boxes
                i = boxes.length;
                overlapping = false;
                while (i--) {
                    if (i > 0 && boxes[i - 1].pos + boxes[i - 1].size > boxes[i].pos) { // Overlap
                        boxes[i - 1].size += boxes[i].size; // Add this size to the previous box
                        boxes[i - 1].targets = boxes[i - 1].targets.concat(boxes[i].targets);

                        // Overlapping right, push left
                        if (boxes[i - 1].pos + boxes[i - 1].size > len) {
                            boxes[i - 1].pos = len - boxes[i - 1].size;
                        }
                        boxes.splice(i, 1); // Remove this item
                        overlapping = true;
                    }
                }
            }

            // Now the composite boxes are placed, we need to put the original boxes within them
            i = 0;
            each(boxes, function(box) {
                var posInCompositeBox = 0;
                each(box.targets, function() {
                    origBoxes[i].pos = box.pos + posInCompositeBox;
                    posInCompositeBox += origBoxes[i].size;
                    i++;
                });
            });

            // Add the rest (hidden) boxes and sort by target
            origBoxes.push.apply(origBoxes, restBoxes);
            stableSort(origBoxes, sortByTarget);
        };


        /**
         * Draw the data labels
         */
        Series.prototype.drawDataLabels = function() {

            var series = this,
                seriesOptions = series.options,
                options = seriesOptions.dataLabels,
                points = series.points,
                pointOptions,
                generalOptions,
                hasRendered = series.hasRendered || 0,
                str,
                dataLabelsGroup,
                defer = pick(options.defer, true),
                renderer = series.chart.renderer;

            if (options.enabled || series._hasPointLabels) {

                // Process default alignment of data labels for columns
                if (series.dlProcessOptions) {
                    series.dlProcessOptions(options);
                }

                // Create a separate group for the data labels to avoid rotation
                dataLabelsGroup = series.plotGroup(
                    'dataLabelsGroup',
                    'data-labels',
                    defer && !hasRendered ? 'hidden' : 'visible', // #5133
                    options.zIndex || 6
                );

                if (defer) {
                    dataLabelsGroup.attr({
                        opacity: +hasRendered
                    }); // #3300
                    if (!hasRendered) {
                        addEvent(series, 'afterAnimate', function() {
                            if (series.visible) { // #2597, #3023, #3024
                                dataLabelsGroup.show(true);
                            }
                            dataLabelsGroup[seriesOptions.animation ? 'animate' : 'attr']({
                                opacity: 1
                            }, {
                                duration: 200
                            });
                        });
                    }
                }

                // Make the labels for each point
                generalOptions = options;
                each(points, function(point) {

                    var enabled,
                        dataLabel = point.dataLabel,
                        labelConfig,
                        attr,
                        name,
                        rotation,
                        connector = point.connector,
                        isNew = true,
                        style,
                        moreStyle = {};

                    // Determine if each data label is enabled
                    pointOptions = point.dlOptions || (point.options && point.options.dataLabels); // dlOptions is used in treemaps
                    enabled = pick(pointOptions && pointOptions.enabled, generalOptions.enabled) && point.y !== null; // #2282, #4641


                    // If the point is outside the plot area, destroy it. #678, #820
                    if (dataLabel && !enabled) {
                        point.dataLabel = dataLabel.destroy();

                        // Individual labels are disabled if the are explicitly disabled
                        // in the point options, or if they fall outside the plot area.
                    } else if (enabled) {

                        // Create individual options structure that can be extended without
                        // affecting others
                        options = merge(generalOptions, pointOptions);
                        style = options.style;

                        rotation = options.rotation;

                        // Get the string
                        labelConfig = point.getLabelConfig();
                        str = options.format ?
                            format(options.format, labelConfig) :
                            options.formatter.call(labelConfig, options);


                        // Determine the color
                        style.color = pick(options.color, style.color, series.color, '#000000');


                        // update existing label
                        if (dataLabel) {

                            if (defined(str)) {
                                dataLabel
                                    .attr({
                                        text: str
                                    });
                                isNew = false;

                            } else { // #1437 - the label is shown conditionally
                                point.dataLabel = dataLabel = dataLabel.destroy();
                                if (connector) {
                                    point.connector = connector.destroy();
                                }
                            }

                            // create new label
                        } else if (defined(str)) {
                            attr = {
                                //align: align,

                                fill: options.backgroundColor,
                                stroke: options.borderColor,
                                'stroke-width': options.borderWidth,

                                r: options.borderRadius || 0,
                                rotation: rotation,
                                padding: options.padding,
                                zIndex: 1
                            };


                            // Get automated contrast color
                            if (style.color === 'contrast') {
                                moreStyle.color = options.inside || options.distance < 0 || !!seriesOptions.stacking ?
                                    renderer.getContrast(point.color || series.color) :
                                    '#000000';
                            }

                            if (seriesOptions.cursor) {
                                moreStyle.cursor = seriesOptions.cursor;
                            }



                            // Remove unused attributes (#947)
                            for (name in attr) {
                                if (attr[name] === undefined) {
                                    delete attr[name];
                                }
                            }

                            dataLabel = point.dataLabel = renderer[rotation ? 'text' : 'label']( // labels don't support rotation
                                    str,
                                    0, -9999,
                                    options.shape,
                                    null,
                                    null,
                                    options.useHTML,
                                    null,
                                    'data-label'
                                )
                                .attr(attr);

                            dataLabel.addClass('highcharts-data-label-color-' + point.colorIndex + ' ' + (options.className || ''));


                            // Styles must be applied before add in order to read text bounding box
                            dataLabel.css(extend(style, moreStyle));


                            dataLabel.add(dataLabelsGroup);


                            dataLabel.shadow(options.shadow);



                        }

                        if (dataLabel) {
                            // Now the data label is created and placed at 0,0, so we need to align it
                            series.alignDataLabel(point, dataLabel, options, null, isNew);
                        }
                    }
                });
            }
        };

        /**
         * Align each individual data label
         */
        Series.prototype.alignDataLabel = function(point, dataLabel, options, alignTo, isNew) {
            var chart = this.chart,
                inverted = chart.inverted,
                plotX = pick(point.plotX, -9999),
                plotY = pick(point.plotY, -9999),
                bBox = dataLabel.getBBox(),
                fontSize,
                baseline,
                rotation = options.rotation,
                normRotation,
                negRotation,
                align = options.align,
                rotCorr, // rotation correction
                // Math.round for rounding errors (#2683), alignTo to allow column labels (#2700)
                visible = this.visible && (point.series.forceDL || chart.isInsidePlot(plotX, Math.round(plotY), inverted) ||
                    (alignTo && chart.isInsidePlot(plotX, inverted ? alignTo.x + 1 : alignTo.y + alignTo.height - 1, inverted))),
                alignAttr, // the final position;
                justify = pick(options.overflow, 'justify') === 'justify';

            if (visible) {


                fontSize = options.style.fontSize;


                baseline = chart.renderer.fontMetrics(fontSize, dataLabel).b;

                // The alignment box is a singular point
                alignTo = extend({
                    x: inverted ? chart.plotWidth - plotY : plotX,
                    y: Math.round(inverted ? chart.plotHeight - plotX : plotY),
                    width: 0,
                    height: 0
                }, alignTo);

                // Add the text size for alignment calculation
                extend(options, {
                    width: bBox.width,
                    height: bBox.height
                });

                // Allow a hook for changing alignment in the last moment, then do the alignment
                if (rotation) {
                    justify = false; // Not supported for rotated text
                    rotCorr = chart.renderer.rotCorr(baseline, rotation); // #3723
                    alignAttr = {
                        x: alignTo.x + options.x + alignTo.width / 2 + rotCorr.x,
                        y: alignTo.y + options.y + {
                            top: 0,
                            middle: 0.5,
                            bottom: 1
                        }[options.verticalAlign] * alignTo.height
                    };
                    dataLabel[isNew ? 'attr' : 'animate'](alignAttr)
                        .attr({ // #3003
                            align: align
                        });

                    // Compensate for the rotated label sticking out on the sides
                    normRotation = (rotation + 720) % 360;
                    negRotation = normRotation > 180 && normRotation < 360;

                    if (align === 'left') {
                        alignAttr.y -= negRotation ? bBox.height : 0;
                    } else if (align === 'center') {
                        alignAttr.x -= bBox.width / 2;
                        alignAttr.y -= bBox.height / 2;
                    } else if (align === 'right') {
                        alignAttr.x -= bBox.width;
                        alignAttr.y -= negRotation ? 0 : bBox.height;
                    }


                } else {
                    dataLabel.align(options, null, alignTo);
                    alignAttr = dataLabel.alignAttr;
                }

                // Handle justify or crop
                if (justify) {
                    this.justifyDataLabel(dataLabel, options, alignAttr, bBox, alignTo, isNew);

                    // Now check that the data label is within the plot area
                } else if (pick(options.crop, true)) {
                    visible = chart.isInsidePlot(alignAttr.x, alignAttr.y) && chart.isInsidePlot(alignAttr.x + bBox.width, alignAttr.y + bBox.height);
                }

                // When we're using a shape, make it possible with a connector or an arrow pointing to thie point
                if (options.shape && !rotation) {
                    dataLabel.attr({
                        anchorX: point.plotX,
                        anchorY: point.plotY
                    });
                }
            }

            // Show or hide based on the final aligned position
            if (!visible) {
                dataLabel.attr({
                    y: -9999
                });
                dataLabel.placed = false; // don't animate back in
            }

        };

        /**
         * If data labels fall partly outside the plot area, align them back in, in a way that
         * doesn't hide the point.
         */
        Series.prototype.justifyDataLabel = function(dataLabel, options, alignAttr, bBox, alignTo, isNew) {
            var chart = this.chart,
                align = options.align,
                verticalAlign = options.verticalAlign,
                off,
                justified,
                padding = dataLabel.box ? 0 : (dataLabel.padding || 0);

            // Off left
            off = alignAttr.x + padding;
            if (off < 0) {
                if (align === 'right') {
                    options.align = 'left';
                } else {
                    options.x = -off;
                }
                justified = true;
            }

            // Off right
            off = alignAttr.x + bBox.width - padding;
            if (off > chart.plotWidth) {
                if (align === 'left') {
                    options.align = 'right';
                } else {
                    options.x = chart.plotWidth - off;
                }
                justified = true;
            }

            // Off top
            off = alignAttr.y + padding;
            if (off < 0) {
                if (verticalAlign === 'bottom') {
                    options.verticalAlign = 'top';
                } else {
                    options.y = -off;
                }
                justified = true;
            }

            // Off bottom
            off = alignAttr.y + bBox.height - padding;
            if (off > chart.plotHeight) {
                if (verticalAlign === 'top') {
                    options.verticalAlign = 'bottom';
                } else {
                    options.y = chart.plotHeight - off;
                }
                justified = true;
            }

            if (justified) {
                dataLabel.placed = !isNew;
                dataLabel.align(options, null, alignTo);
            }
        };

        /**
         * Override the base drawDataLabels method by pie specific functionality
         */
        if (seriesTypes.pie) {
            seriesTypes.pie.prototype.drawDataLabels = function() {
                var series = this,
                    data = series.data,
                    point,
                    chart = series.chart,
                    options = series.options.dataLabels,
                    connectorPadding = pick(options.connectorPadding, 10),
                    connectorWidth = pick(options.connectorWidth, 1),
                    plotWidth = chart.plotWidth,
                    plotHeight = chart.plotHeight,
                    connector,
                    distanceOption = options.distance,
                    seriesCenter = series.center,
                    radius = seriesCenter[2] / 2,
                    centerY = seriesCenter[1],
                    outside = distanceOption > 0,
                    dataLabel,
                    dataLabelWidth,
                    labelPos,
                    labelHeight,
                    halves = [ // divide the points into right and left halves for anti collision
                        [], // right
                        [] // left
                    ],
                    x,
                    y,
                    visibility,
                    j,
                    overflow = [0, 0, 0, 0]; // top, right, bottom, left

                // get out if not enabled
                if (!series.visible || (!options.enabled && !series._hasPointLabels)) {
                    return;
                }

                // run parent method
                Series.prototype.drawDataLabels.apply(series);

                each(data, function(point) {
                    if (point.dataLabel && point.visible) { // #407, #2510

                        // Arrange points for detection collision
                        halves[point.half].push(point);

                        // Reset positions (#4905)
                        point.dataLabel._pos = null;
                    }
                });

                /* Loop over the points in each half, starting from the top and bottom
                 * of the pie to detect overlapping labels.
                 */
                each(halves, function(points, i) {

                    var top,
                        bottom,
                        length = points.length,
                        positions,
                        naturalY,
                        size;

                    if (!length) {
                        return;
                    }

                    // Sort by angle
                    series.sortByAngle(points, i - 0.5);

                    // Only do anti-collision when we are outside the pie and have connectors (#856)
                    if (distanceOption > 0) {
                        top = Math.max(0, centerY - radius - distanceOption);
                        bottom = Math.min(centerY + radius + distanceOption, chart.plotHeight);
                        positions = map(points, function(point) {
                            if (point.dataLabel) {
                                size = point.dataLabel.getBBox().height || 21;
                                return {
                                    target: point.labelPos[1] - top + size / 2,
                                    size: size,
                                    rank: point.y
                                };
                            }
                        });
                        H.distribute(positions, bottom + size - top);
                    }

                    // now the used slots are sorted, fill them up sequentially
                    for (j = 0; j < length; j++) {

                        point = points[j];
                        labelPos = point.labelPos;
                        dataLabel = point.dataLabel;
                        visibility = point.visible === false ? 'hidden' : 'inherit';
                        naturalY = labelPos[1];

                        if (positions) {
                            if (positions[j].pos === undefined) {
                                visibility = 'hidden';
                            } else {
                                labelHeight = positions[j].size;
                                y = top + positions[j].pos;
                            }

                        } else {
                            y = naturalY;
                        }

                        // get the x - use the natural x position for labels near the top and bottom, to prevent the top
                        // and botton slice connectors from touching each other on either side
                        if (options.justify) {
                            x = seriesCenter[0] + (i ? -1 : 1) * (radius + distanceOption);
                        } else {
                            x = series.getX(y < top + 2 || y > bottom - 2 ? naturalY : y, i);
                        }


                        // Record the placement and visibility
                        dataLabel._attr = {
                            visibility: visibility,
                            align: labelPos[6]
                        };
                        dataLabel._pos = {
                            x: x + options.x +
                                ({
                                    left: connectorPadding,
                                    right: -connectorPadding
                                }[labelPos[6]] || 0),
                            y: y + options.y - 10 // 10 is for the baseline (label vs text)
                        };
                        labelPos.x = x;
                        labelPos.y = y;


                        // Detect overflowing data labels
                        if (series.options.size === null) {
                            dataLabelWidth = dataLabel.width;
                            // Overflow left
                            if (x - dataLabelWidth < connectorPadding) {
                                overflow[3] = Math.max(Math.round(dataLabelWidth - x + connectorPadding), overflow[3]);

                                // Overflow right
                            } else if (x + dataLabelWidth > plotWidth - connectorPadding) {
                                overflow[1] = Math.max(Math.round(x + dataLabelWidth - plotWidth + connectorPadding), overflow[1]);
                            }

                            // Overflow top
                            if (y - labelHeight / 2 < 0) {
                                overflow[0] = Math.max(Math.round(-y + labelHeight / 2), overflow[0]);

                                // Overflow left
                            } else if (y + labelHeight / 2 > plotHeight) {
                                overflow[2] = Math.max(Math.round(y + labelHeight / 2 - plotHeight), overflow[2]);
                            }
                        }
                    } // for each point
                }); // for each half

                // Do not apply the final placement and draw the connectors until we have verified
                // that labels are not spilling over.
                if (arrayMax(overflow) === 0 || this.verifyDataLabelOverflow(overflow)) {

                    // Place the labels in the final position
                    this.placeDataLabels();

                    // Draw the connectors
                    if (outside && connectorWidth) {
                        each(this.points, function(point) {
                            var isNew;

                            connector = point.connector;
                            dataLabel = point.dataLabel;

                            if (dataLabel && dataLabel._pos && point.visible) {
                                visibility = dataLabel._attr.visibility;

                                isNew = !connector;

                                if (isNew) {
                                    point.connector = connector = chart.renderer.path()
                                        .addClass('highcharts-data-label-connector highcharts-color-' + point.colorIndex)
                                        .add(series.dataLabelsGroup);


                                    connector.attr({
                                        'stroke-width': connectorWidth,
                                        'stroke': options.connectorColor || point.color || '#666666'
                                    });

                                }
                                connector[isNew ? 'attr' : 'animate']({
                                    d: series.connectorPath(point.labelPos)
                                });
                                connector.attr('visibility', visibility);

                            } else if (connector) {
                                point.connector = connector.destroy();
                            }
                        });
                    }
                }
            };

            /**
             * Extendable method for getting the path of the connector between the data label
             * and the pie slice.
             */
            seriesTypes.pie.prototype.connectorPath = function(labelPos) {
                var x = labelPos.x,
                    y = labelPos.y;
                return pick(this.options.dataLabels.softConnector, true) ? [
                    'M',
                    x + (labelPos[6] === 'left' ? 5 : -5), y, // end of the string at the label
                    'C',
                    x, y, // first break, next to the label
                    2 * labelPos[2] - labelPos[4], 2 * labelPos[3] - labelPos[5],
                    labelPos[2], labelPos[3], // second break
                    'L',
                    labelPos[4], labelPos[5] // base
                ] : [
                    'M',
                    x + (labelPos[6] === 'left' ? 5 : -5), y, // end of the string at the label
                    'L',
                    labelPos[2], labelPos[3], // second break
                    'L',
                    labelPos[4], labelPos[5] // base
                ];
            };

            /**
             * Perform the final placement of the data labels after we have verified that they
             * fall within the plot area.
             */
            seriesTypes.pie.prototype.placeDataLabels = function() {
                each(this.points, function(point) {
                    var dataLabel = point.dataLabel,
                        _pos;

                    if (dataLabel && point.visible) {
                        _pos = dataLabel._pos;
                        if (_pos) {
                            dataLabel.attr(dataLabel._attr);
                            dataLabel[dataLabel.moved ? 'animate' : 'attr'](_pos);
                            dataLabel.moved = true;
                        } else if (dataLabel) {
                            dataLabel.attr({
                                y: -9999
                            });
                        }
                    }
                });
            };

            seriesTypes.pie.prototype.alignDataLabel = noop;

            /**
             * Verify whether the data labels are allowed to draw, or we should run more translation and data
             * label positioning to keep them inside the plot area. Returns true when data labels are ready
             * to draw.
             */
            seriesTypes.pie.prototype.verifyDataLabelOverflow = function(overflow) {

                var center = this.center,
                    options = this.options,
                    centerOption = options.center,
                    minSize = options.minSize || 80,
                    newSize = minSize,
                    ret;

                // Handle horizontal size and center
                if (centerOption[0] !== null) { // Fixed center
                    newSize = Math.max(center[2] - Math.max(overflow[1], overflow[3]), minSize);

                } else { // Auto center
                    newSize = Math.max(
                        center[2] - overflow[1] - overflow[3], // horizontal overflow
                        minSize
                    );
                    center[0] += (overflow[3] - overflow[1]) / 2; // horizontal center
                }

                // Handle vertical size and center
                if (centerOption[1] !== null) { // Fixed center
                    newSize = Math.max(Math.min(newSize, center[2] - Math.max(overflow[0], overflow[2])), minSize);

                } else { // Auto center
                    newSize = Math.max(
                        Math.min(
                            newSize,
                            center[2] - overflow[0] - overflow[2] // vertical overflow
                        ),
                        minSize
                    );
                    center[1] += (overflow[0] - overflow[2]) / 2; // vertical center
                }

                // If the size must be decreased, we need to run translate and drawDataLabels again
                if (newSize < center[2]) {
                    center[2] = newSize;
                    center[3] = Math.min(relativeLength(options.innerSize || 0, newSize), newSize); // #3632
                    this.translate(center);

                    if (this.drawDataLabels) {
                        this.drawDataLabels();
                    }
                    // Else, return true to indicate that the pie and its labels is within the plot area
                } else {
                    ret = true;
                }
                return ret;
            };
        }

        if (seriesTypes.column) {

            /**
             * Override the basic data label alignment by adjusting for the position of the column
             */
            seriesTypes.column.prototype.alignDataLabel = function(point, dataLabel, options, alignTo, isNew) {
                var inverted = this.chart.inverted,
                    series = point.series,
                    dlBox = point.dlBox || point.shapeArgs, // data label box for alignment
                    below = pick(point.below, point.plotY > pick(this.translatedThreshold, series.yAxis.len)), // point.below is used in range series
                    inside = pick(options.inside, !!this.options.stacking), // draw it inside the box?
                    overshoot;

                // Align to the column itself, or the top of it
                if (dlBox) { // Area range uses this method but not alignTo
                    alignTo = merge(dlBox);

                    if (alignTo.y < 0) {
                        alignTo.height += alignTo.y;
                        alignTo.y = 0;
                    }
                    overshoot = alignTo.y + alignTo.height - series.yAxis.len;
                    if (overshoot > 0) {
                        alignTo.height -= overshoot;
                    }

                    if (inverted) {
                        alignTo = {
                            x: series.yAxis.len - alignTo.y - alignTo.height,
                            y: series.xAxis.len - alignTo.x - alignTo.width,
                            width: alignTo.height,
                            height: alignTo.width
                        };
                    }

                    // Compute the alignment box
                    if (!inside) {
                        if (inverted) {
                            alignTo.x += below ? 0 : alignTo.width;
                            alignTo.width = 0;
                        } else {
                            alignTo.y += below ? alignTo.height : 0;
                            alignTo.height = 0;
                        }
                    }
                }


                // When alignment is undefined (typically columns and bars), display the individual
                // point below or above the point depending on the threshold
                options.align = pick(
                    options.align, !inverted || inside ? 'center' : below ? 'right' : 'left'
                );
                options.verticalAlign = pick(
                    options.verticalAlign,
                    inverted || inside ? 'middle' : below ? 'top' : 'bottom'
                );

                // Call the parent method
                Series.prototype.alignDataLabel.call(this, point, dataLabel, options, alignTo, isNew);
            };
        }

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2009-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        /**
         * Highcharts module to hide overlapping data labels. This module is included in Highcharts.
         */
        var Chart = H.Chart,
            each = H.each,
            pick = H.pick,
            addEvent = H.addEvent;

        // Collect potensial overlapping data labels. Stack labels probably don't need to be 
        // considered because they are usually accompanied by data labels that lie inside the columns.
        Chart.prototype.callbacks.push(function(chart) {
            function collectAndHide() {
                var labels = [];

                each(chart.series, function(series) {
                    var dlOptions = series.options.dataLabels,
                        collections = series.dataLabelCollections || ['dataLabel']; // Range series have two collections
                    if ((dlOptions.enabled || series._hasPointLabels) && !dlOptions.allowOverlap && series.visible) { // #3866
                        each(collections, function(coll) {
                            each(series.points, function(point) {
                                if (point[coll]) {
                                    point[coll].labelrank = pick(point.labelrank, point.shapeArgs && point.shapeArgs.height); // #4118
                                    labels.push(point[coll]);
                                }
                            });
                        });
                    }
                });
                chart.hideOverlappingLabels(labels);
            }

            // Do it now ...
            collectAndHide();

            // ... and after each chart redraw
            addEvent(chart, 'redraw', collectAndHide);

        });

        /**
         * Hide overlapping labels. Labels are moved and faded in and out on zoom to provide a smooth 
         * visual imression.
         */
        Chart.prototype.hideOverlappingLabels = function(labels) {

            var len = labels.length,
                label,
                i,
                j,
                label1,
                label2,
                isIntersecting,
                pos1,
                pos2,
                parent1,
                parent2,
                padding,
                intersectRect = function(x1, y1, w1, h1, x2, y2, w2, h2) {
                    return !(
                        x2 > x1 + w1 ||
                        x2 + w2 < x1 ||
                        y2 > y1 + h1 ||
                        y2 + h2 < y1
                    );
                };

            // Mark with initial opacity
            for (i = 0; i < len; i++) {
                label = labels[i];
                if (label) {
                    label.oldOpacity = label.opacity;
                    label.newOpacity = 1;
                }
            }

            // Prevent a situation in a gradually rising slope, that each label
            // will hide the previous one because the previous one always has
            // lower rank.
            labels.sort(function(a, b) {
                return (b.labelrank || 0) - (a.labelrank || 0);
            });

            // Detect overlapping labels
            for (i = 0; i < len; i++) {
                label1 = labels[i];

                for (j = i + 1; j < len; ++j) {
                    label2 = labels[j];
                    if (label1 && label2 && label1.placed && label2.placed && label1.newOpacity !== 0 && label2.newOpacity !== 0) {
                        pos1 = label1.alignAttr;
                        pos2 = label2.alignAttr;
                        parent1 = label1.parentGroup; // Different panes have different positions
                        parent2 = label2.parentGroup;
                        padding = 2 * (label1.box ? 0 : label1.padding); // Substract the padding if no background or border (#4333)
                        isIntersecting = intersectRect(
                            pos1.x + parent1.translateX,
                            pos1.y + parent1.translateY,
                            label1.width - padding,
                            label1.height - padding,
                            pos2.x + parent2.translateX,
                            pos2.y + parent2.translateY,
                            label2.width - padding,
                            label2.height - padding
                        );

                        if (isIntersecting) {
                            (label1.labelrank < label2.labelrank ? label1 : label2).newOpacity = 0;
                        }
                    }
                }
            }

            // Hide or show
            each(labels, function(label) {
                var complete,
                    newOpacity;

                if (label) {
                    newOpacity = label.newOpacity;

                    if (label.oldOpacity !== newOpacity && label.placed) {

                        // Make sure the label is completely hidden to avoid catching clicks (#4362)
                        if (newOpacity) {
                            label.show(true);
                        } else {
                            complete = function() {
                                label.hide();
                            };
                        }

                        // Animate or set the opacity					
                        label.alignAttr.opacity = newOpacity;
                        label[label.isOld ? 'animate' : 'attr'](label.alignAttr, null, complete);

                    }
                    label.isOld = true;
                }
            });
        };

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var addEvent = H.addEvent,
            Chart = H.Chart,
            createElement = H.createElement,
            css = H.css,
            defaultOptions = H.defaultOptions,
            defaultPlotOptions = H.defaultPlotOptions,
            each = H.each,
            extend = H.extend,
            fireEvent = H.fireEvent,
            hasTouch = H.hasTouch,
            inArray = H.inArray,
            isObject = H.isObject,
            Legend = H.Legend,
            merge = H.merge,
            pick = H.pick,
            Point = H.Point,
            Series = H.Series,
            seriesTypes = H.seriesTypes,
            svg = H.svg,
            TrackerMixin;

        /**
         * TrackerMixin for points and graphs.
         *
         * @mixin
         */
        TrackerMixin = H.TrackerMixin = {

            /**
             * Draw the tracker for a point.
             */
            drawTrackerPoint: function() {
                var series = this,
                    chart = series.chart,
                    pointer = chart.pointer,
                    onMouseOver = function(e) {
                        var target = e.target,
                            point;

                        while (target && !point) {
                            point = target.point;
                            target = target.parentNode;
                        }

                        if (point !== undefined && point !== chart.hoverPoint) { // undefined on graph in scatterchart
                            point.onMouseOver(e);
                        }
                    };

                // Add reference to the point
                each(series.points, function(point) {
                    if (point.graphic) {
                        point.graphic.element.point = point;
                    }
                    if (point.dataLabel) {
                        point.dataLabel.element.point = point;
                    }
                });

                // Add the event listeners, we need to do this only once
                if (!series._hasTracking) {
                    each(series.trackerGroups, function(key) {
                        if (series[key]) { // we don't always have dataLabelsGroup
                            series[key]
                                .addClass('highcharts-tracker')
                                .on('mouseover', onMouseOver)
                                .on('mouseout', function(e) {
                                    pointer.onTrackerMouseOut(e);
                                });
                            if (hasTouch) {
                                series[key].on('touchstart', onMouseOver);
                            }


                            if (series.options.cursor) {
                                series[key]
                                    .css(css)
                                    .css({
                                        cursor: series.options.cursor
                                    });
                            }

                        }
                    });
                    series._hasTracking = true;
                }
            },

            /**
             * Draw the tracker object that sits above all data labels and markers to
             * track mouse events on the graph or points. For the line type charts
             * the tracker uses the same graphPath, but with a greater stroke width
             * for better control.
             */
            drawTrackerGraph: function() {
                var series = this,
                    options = series.options,
                    trackByArea = options.trackByArea,
                    trackerPath = [].concat(trackByArea ? series.areaPath : series.graphPath),
                    trackerPathLength = trackerPath.length,
                    chart = series.chart,
                    pointer = chart.pointer,
                    renderer = chart.renderer,
                    snap = chart.options.tooltip.snap,
                    tracker = series.tracker,
                    i,
                    onMouseOver = function() {
                        if (chart.hoverSeries !== series) {
                            series.onMouseOver();
                        }
                    },
                    /*
                     * Empirical lowest possible opacities for TRACKER_FILL for an element to stay invisible but clickable
                     * IE6: 0.002
                     * IE7: 0.002
                     * IE8: 0.002
                     * IE9: 0.00000000001 (unlimited)
                     * IE10: 0.0001 (exporting only)
                     * FF: 0.00000000001 (unlimited)
                     * Chrome: 0.000001
                     * Safari: 0.000001
                     * Opera: 0.00000000001 (unlimited)
                     */
                    TRACKER_FILL = 'rgba(192,192,192,' + (svg ? 0.0001 : 0.002) + ')';

                // Extend end points. A better way would be to use round linecaps,
                // but those are not clickable in VML.
                if (trackerPathLength && !trackByArea) {
                    i = trackerPathLength + 1;
                    while (i--) {
                        if (trackerPath[i] === 'M') { // extend left side
                            trackerPath.splice(i + 1, 0, trackerPath[i + 1] - snap, trackerPath[i + 2], 'L');
                        }
                        if ((i && trackerPath[i] === 'M') || i === trackerPathLength) { // extend right side
                            trackerPath.splice(i, 0, 'L', trackerPath[i - 2] + snap, trackerPath[i - 1]);
                        }
                    }
                }

                // handle single points
                /*for (i = 0; i < singlePoints.length; i++) {
                	singlePoint = singlePoints[i];
                	trackerPath.push(M, singlePoint.plotX - snap, singlePoint.plotY,
                	L, singlePoint.plotX + snap, singlePoint.plotY);
                }*/

                // draw the tracker
                if (tracker) {
                    tracker.attr({
                        d: trackerPath
                    });
                } else if (series.graph) { // create

                    series.tracker = renderer.path(trackerPath)
                        .attr({
                            'stroke-linejoin': 'round', // #1225
                            visibility: series.visible ? 'visible' : 'hidden',
                            stroke: TRACKER_FILL,
                            fill: trackByArea ? TRACKER_FILL : 'none',
                            'stroke-width': series.graph.strokeWidth() + (trackByArea ? 0 : 2 * snap),
                            zIndex: 2
                        })
                        .add(series.group);

                    // The tracker is added to the series group, which is clipped, but is covered
                    // by the marker group. So the marker group also needs to capture events.
                    each([series.tracker, series.markerGroup], function(tracker) {
                        tracker.addClass('highcharts-tracker')
                            .on('mouseover', onMouseOver)
                            .on('mouseout', function(e) {
                                pointer.onTrackerMouseOut(e);
                            });


                        if (options.cursor) {
                            tracker.css({
                                cursor: options.cursor
                            });
                        }


                        if (hasTouch) {
                            tracker.on('touchstart', onMouseOver);
                        }
                    });
                }
            }
        };
        /* End TrackerMixin */


        /**
         * Add tracking event listener to the series group, so the point graphics
         * themselves act as trackers
         */

        if (seriesTypes.column) {
            seriesTypes.column.prototype.drawTracker = TrackerMixin.drawTrackerPoint;
        }

        if (seriesTypes.pie) {
            seriesTypes.pie.prototype.drawTracker = TrackerMixin.drawTrackerPoint;
        }

        if (seriesTypes.scatter) {
            seriesTypes.scatter.prototype.drawTracker = TrackerMixin.drawTrackerPoint;
        }

        /*
         * Extend Legend for item events
         */
        extend(Legend.prototype, {

            setItemEvents: function(item, legendItem, useHTML) {
                var legend = this,
                    chart = legend.chart,
                    activeClass = 'highcharts-legend-' + (item.series ? 'point' : 'series') + '-active';

                // Set the events on the item group, or in case of useHTML, the item itself (#1249)
                (useHTML ? legendItem : item.legendGroup).on('mouseover', function() {
                        item.setState('hover');

                        // A CSS class to dim or hide other than the hovered series
                        chart.seriesGroup.addClass(activeClass);


                        legendItem.css(legend.options.itemHoverStyle);

                    })
                    .on('mouseout', function() {

                        legendItem.css(item.visible ? legend.itemStyle : legend.itemHiddenStyle);


                        // A CSS class to dim or hide other than the hovered series
                        chart.seriesGroup.removeClass(activeClass);

                        item.setState();
                    })
                    .on('click', function(event) {
                        var strLegendItemClick = 'legendItemClick',
                            fnLegendItemClick = function() {
                                if (item.setVisible) {
                                    item.setVisible();
                                }
                            };

                        // Pass over the click/touch event. #4.
                        event = {
                            browserEvent: event
                        };

                        // click the name or symbol
                        if (item.firePointEvent) { // point
                            item.firePointEvent(strLegendItemClick, event, fnLegendItemClick);
                        } else {
                            fireEvent(item, strLegendItemClick, event, fnLegendItemClick);
                        }
                    });
            },

            createCheckboxForItem: function(item) {
                var legend = this;

                item.checkbox = createElement('input', {
                    type: 'checkbox',
                    checked: item.selected,
                    defaultChecked: item.selected // required by IE7
                }, legend.options.itemCheckboxStyle, legend.chart.container);

                addEvent(item.checkbox, 'click', function(event) {
                    var target = event.target;
                    fireEvent(
                        item.series || item,
                        'checkboxClick', { // #3712
                            checked: target.checked,
                            item: item
                        },
                        function() {
                            item.select();
                        }
                    );
                });
            }
        });



        // Add pointer cursor to legend itemstyle in defaultOptions
        defaultOptions.legend.itemStyle.cursor = 'pointer';



        /*
         * Extend the Chart object with interaction
         */

        extend(Chart.prototype, /** @lends Chart.prototype */ {
            /**
             * Display the zoom button
             */
            showResetZoom: function() {
                var chart = this,
                    lang = defaultOptions.lang,
                    btnOptions = chart.options.chart.resetZoomButton,
                    theme = btnOptions.theme,
                    states = theme.states,
                    alignTo = btnOptions.relativeTo === 'chart' ? null : 'plotBox';

                function zoomOut() {
                    chart.zoomOut();
                }

                this.resetZoomButton = chart.renderer.button(lang.resetZoom, null, null, zoomOut, theme, states && states.hover)
                    .attr({
                        align: btnOptions.position.align,
                        title: lang.resetZoomTitle
                    })
                    .addClass('highcharts-reset-zoom')
                    .add()
                    .align(btnOptions.position, false, alignTo);

            },

            /**
             * Zoom out to 1:1
             */
            zoomOut: function() {
                var chart = this;
                fireEvent(chart, 'selection', {
                    resetSelection: true
                }, function() {
                    chart.zoom();
                });
            },

            /**
             * Zoom into a given portion of the chart given by axis coordinates
             * @param {Object} event
             */
            zoom: function(event) {
                var chart = this,
                    hasZoomed,
                    pointer = chart.pointer,
                    displayButton = false,
                    resetZoomButton;

                // If zoom is called with no arguments, reset the axes
                if (!event || event.resetSelection) {
                    each(chart.axes, function(axis) {
                        hasZoomed = axis.zoom();
                    });
                } else { // else, zoom in on all axes
                    each(event.xAxis.concat(event.yAxis), function(axisData) {
                        var axis = axisData.axis,
                            isXAxis = axis.isXAxis;

                        // don't zoom more than minRange
                        if (pointer[isXAxis ? 'zoomX' : 'zoomY']) {
                            hasZoomed = axis.zoom(axisData.min, axisData.max);
                            if (axis.displayBtn) {
                                displayButton = true;
                            }
                        }
                    });
                }

                // Show or hide the Reset zoom button
                resetZoomButton = chart.resetZoomButton;
                if (displayButton && !resetZoomButton) {
                    chart.showResetZoom();
                } else if (!displayButton && isObject(resetZoomButton)) {
                    chart.resetZoomButton = resetZoomButton.destroy();
                }


                // Redraw
                if (hasZoomed) {
                    chart.redraw(
                        pick(chart.options.chart.animation, event && event.animation, chart.pointCount < 100) // animation
                    );
                }
            },

            /**
             * Pan the chart by dragging the mouse across the pane. This function is called
             * on mouse move, and the distance to pan is computed from chartX compared to
             * the first chartX position in the dragging operation.
             */
            pan: function(e, panning) {

                var chart = this,
                    hoverPoints = chart.hoverPoints,
                    doRedraw;

                // remove active points for shared tooltip
                if (hoverPoints) {
                    each(hoverPoints, function(point) {
                        point.setState();
                    });
                }

                each(panning === 'xy' ? [1, 0] : [1], function(isX) { // xy is used in maps
                    var axis = chart[isX ? 'xAxis' : 'yAxis'][0],
                        horiz = axis.horiz,
                        mousePos = e[horiz ? 'chartX' : 'chartY'],
                        mouseDown = horiz ? 'mouseDownX' : 'mouseDownY',
                        startPos = chart[mouseDown],
                        halfPointRange = (axis.pointRange || 0) / 2,
                        extremes = axis.getExtremes(),
                        newMin = axis.toValue(startPos - mousePos, true) + halfPointRange,
                        newMax = axis.toValue(startPos + axis.len - mousePos, true) - halfPointRange,
                        goingLeft = startPos > mousePos; // #3613

                    if (axis.series.length &&
                        (goingLeft || newMin > Math.min(extremes.dataMin, extremes.min)) &&
                        (!goingLeft || newMax < Math.max(extremes.dataMax, extremes.max))) {
                        axis.setExtremes(newMin, newMax, false, false, {
                            trigger: 'pan'
                        });
                        doRedraw = true;
                    }

                    chart[mouseDown] = mousePos; // set new reference for next run
                });

                if (doRedraw) {
                    chart.redraw(false);
                }
                css(chart.container, {
                    cursor: 'move'
                });
            }
        });

        /*
         * Extend the Point object with interaction
         */
        extend(Point.prototype, /** @lends Point.prototype */ {
            /**
             * Toggle the selection status of a point
             * @param {Boolean} selected Whether to select or unselect the point.
             * @param {Boolean} accumulate Whether to add to the previous selection. By default,
             *		 this happens if the control key (Cmd on Mac) was pressed during clicking.
             */
            select: function(selected, accumulate) {
                var point = this,
                    series = point.series,
                    chart = series.chart;

                selected = pick(selected, !point.selected);

                // fire the event with the default handler
                point.firePointEvent(selected ? 'select' : 'unselect', {
                    accumulate: accumulate
                }, function() {
                    point.selected = point.options.selected = selected;
                    series.options.data[inArray(point, series.data)] = point.options;

                    point.setState(selected && 'select');

                    // unselect all other points unless Ctrl or Cmd + click
                    if (!accumulate) {
                        each(chart.getSelectedPoints(), function(loopPoint) {
                            if (loopPoint.selected && loopPoint !== point) {
                                loopPoint.selected = loopPoint.options.selected = false;
                                series.options.data[inArray(loopPoint, series.data)] = loopPoint.options;
                                loopPoint.setState('');
                                loopPoint.firePointEvent('unselect');
                            }
                        });
                    }
                });
            },

            /**
             * Runs on mouse over the point
             *
             * @param {Object} e The event arguments
             * @param {Boolean} byProximity Falsy for kd points that are closest to the mouse, or to
             *        actually hovered points. True for other points in shared tooltip.
             */
            onMouseOver: function(e, byProximity) {
                var point = this,
                    series = point.series,
                    chart = series.chart,
                    tooltip = chart.tooltip,
                    hoverPoint = chart.hoverPoint;

                if (point.series) { // It may have been destroyed, #4130
                    // In shared tooltip, call mouse over when point/series is actually hovered: (#5766)
                    if (!byProximity) {
                        // set normal state to previous series
                        if (hoverPoint && hoverPoint !== point) {
                            hoverPoint.onMouseOut();
                        }
                        if (chart.hoverSeries !== series) {
                            series.onMouseOver();
                        }
                        chart.hoverPoint = point;
                    }

                    // update the tooltip
                    if (tooltip && (!tooltip.shared || series.noSharedTooltip)) {
                        // hover point only for non shared points: (#5766)
                        point.setState('hover');
                        tooltip.refresh(point, e);
                    } else if (!tooltip) {
                        point.setState('hover');
                    }

                    // trigger the event
                    point.firePointEvent('mouseOver');
                }
            },

            /**
             * Runs on mouse out from the point
             */
            onMouseOut: function() {
                var chart = this.series.chart,
                    hoverPoints = chart.hoverPoints;

                this.firePointEvent('mouseOut');

                if (!hoverPoints || inArray(this, hoverPoints) === -1) { // #887, #2240
                    this.setState();
                    chart.hoverPoint = null;
                }
            },

            /**
             * Import events from the series' and point's options. Only do it on
             * demand, to save processing time on hovering.
             */
            importEvents: function() {
                if (!this.hasImportedEvents) {
                    var point = this,
                        options = merge(point.series.options.point, point.options),
                        events = options.events,
                        eventType;

                    point.events = events;

                    for (eventType in events) {
                        addEvent(point, eventType, events[eventType]);
                    }
                    this.hasImportedEvents = true;

                }
            },

            /**
             * Set the point's state
             * @param {String} state
             */
            setState: function(state, move) {
                var point = this,
                    plotX = Math.floor(point.plotX), // #4586
                    plotY = point.plotY,
                    series = point.series,
                    stateOptions = series.options.states[state] || {},
                    markerOptions = defaultPlotOptions[series.type].marker &&
                    series.options.marker,
                    normalDisabled = markerOptions && markerOptions.enabled === false,
                    markerStateOptions = (markerOptions && markerOptions.states &&
                        markerOptions.states[state]) || {},
                    stateDisabled = markerStateOptions.enabled === false,
                    stateMarkerGraphic = series.stateMarkerGraphic,
                    pointMarker = point.marker || {},
                    chart = series.chart,
                    halo = series.halo,
                    haloOptions,
                    markerAttribs,
                    hasMarkers = markerOptions && series.markerAttribs,
                    newSymbol;

                state = state || ''; // empty string

                if (
                    // already has this state
                    (state === point.state && !move) ||
                    // selected points don't respond to hover
                    (point.selected && state !== 'select') ||
                    // series' state options is disabled
                    (stateOptions.enabled === false) ||
                    // general point marker's state options is disabled
                    (state && (stateDisabled || (normalDisabled && markerStateOptions.enabled === false))) ||
                    // individual point marker's state options is disabled
                    (state && pointMarker.states && pointMarker.states[state] && pointMarker.states[state].enabled === false) // #1610

                ) {
                    return;
                }

                if (hasMarkers) {
                    markerAttribs = series.markerAttribs(point, state);
                }

                // Apply hover styles to the existing point
                if (point.graphic) {

                    if (point.state) {
                        point.graphic.removeClass('highcharts-point-' + point.state);
                    }
                    if (state) {
                        point.graphic.addClass('highcharts-point-' + state);
                    }

                    /*attribs = radius ? { // new symbol attributes (#507, #612)
                    	x: plotX - radius,
                    	y: plotY - radius,
                    	width: 2 * radius,
                    	height: 2 * radius
                    } : {};*/


                    //attribs = merge(series.pointAttribs(point, state), attribs);
                    point.graphic.attr(series.pointAttribs(point, state));


                    if (markerAttribs) {
                        point.graphic.animate(
                            markerAttribs,
                            pick(
                                chart.options.chart.animation, // Turn off globally
                                markerStateOptions.animation,
                                markerOptions.animation
                            )
                        );
                    }

                    // Zooming in from a range with no markers to a range with markers
                    if (stateMarkerGraphic) {
                        stateMarkerGraphic.hide();
                    }
                } else {
                    // if a graphic is not applied to each point in the normal state, create a shared
                    // graphic for the hover state
                    if (state && markerStateOptions) {
                        newSymbol = pointMarker.symbol || series.symbol;

                        // If the point has another symbol than the previous one, throw away the
                        // state marker graphic and force a new one (#1459)
                        if (stateMarkerGraphic && stateMarkerGraphic.currentSymbol !== newSymbol) {
                            stateMarkerGraphic = stateMarkerGraphic.destroy();
                        }

                        // Add a new state marker graphic
                        if (!stateMarkerGraphic) {
                            if (newSymbol) {
                                series.stateMarkerGraphic = stateMarkerGraphic = chart.renderer.symbol(
                                        newSymbol,
                                        markerAttribs.x,
                                        markerAttribs.y,
                                        markerAttribs.width,
                                        markerAttribs.height
                                    )
                                    .add(series.markerGroup);
                                stateMarkerGraphic.currentSymbol = newSymbol;
                            }

                            // Move the existing graphic
                        } else {
                            stateMarkerGraphic[move ? 'animate' : 'attr']({ // #1054
                                x: markerAttribs.x,
                                y: markerAttribs.y
                            });
                        }

                        if (stateMarkerGraphic) {
                            stateMarkerGraphic.attr(series.pointAttribs(point, state));
                        }

                    }

                    if (stateMarkerGraphic) {
                        stateMarkerGraphic[state && chart.isInsidePlot(plotX, plotY, chart.inverted) ? 'show' : 'hide'](); // #2450
                        stateMarkerGraphic.element.point = point; // #4310
                    }
                }

                // Show me your halo
                haloOptions = stateOptions.halo;
                if (haloOptions && haloOptions.size) {
                    if (!halo) {
                        series.halo = halo = chart.renderer.path()
                            // #5818, #5903
                            .add(hasMarkers ? series.markerGroup : series.group);
                    }
                    halo[move ? 'animate' : 'attr']({
                        d: point.haloPath(haloOptions.size)
                    });
                    halo.attr({
                        'class': 'highcharts-halo highcharts-color-' + pick(point.colorIndex, series.colorIndex)
                    });


                    halo.attr(extend({
                        'fill': point.color || series.color,
                        'fill-opacity': haloOptions.opacity,
                        'zIndex': -1 // #4929, IE8 added halo above everything
                    }, haloOptions.attributes));

                } else if (halo) {
                    halo.animate({
                        d: point.haloPath(0)
                    }); // Hide
                }

                point.state = state;
            },

            /**
             * Get the circular path definition for the halo
             * @param  {Number} size The radius of the circular halo.
             * @returns {Array} The path definition
             */
            haloPath: function(size) {
                var series = this.series,
                    chart = series.chart;

                return chart.renderer.symbols.circle(
                    Math.floor(this.plotX) - size,
                    this.plotY - size,
                    size * 2,
                    size * 2
                );
            }
        });

        /*
         * Extend the Series object with interaction
         */

        extend(Series.prototype, /** @lends Series.prototype */ {
            /**
             * Series mouse over handler
             */
            onMouseOver: function() {
                var series = this,
                    chart = series.chart,
                    hoverSeries = chart.hoverSeries;

                // set normal state to previous series
                if (hoverSeries && hoverSeries !== series) {
                    hoverSeries.onMouseOut();
                }

                // trigger the event, but to save processing time,
                // only if defined
                if (series.options.events.mouseOver) {
                    fireEvent(series, 'mouseOver');
                }

                // hover this
                series.setState('hover');
                chart.hoverSeries = series;
            },

            /**
             * Series mouse out handler
             */
            onMouseOut: function() {
                // trigger the event only if listeners exist
                var series = this,
                    options = series.options,
                    chart = series.chart,
                    tooltip = chart.tooltip,
                    hoverPoint = chart.hoverPoint;

                chart.hoverSeries = null; // #182, set to null before the mouseOut event fires

                // trigger mouse out on the point, which must be in this series
                if (hoverPoint) {
                    hoverPoint.onMouseOut();
                }

                // fire the mouse out event
                if (series && options.events.mouseOut) {
                    fireEvent(series, 'mouseOut');
                }


                // hide the tooltip
                if (tooltip && !options.stickyTracking && (!tooltip.shared || series.noSharedTooltip)) {
                    tooltip.hide();
                }

                // set normal state
                series.setState();
            },

            /**
             * Set the state of the graph
             */
            setState: function(state) {
                var series = this,
                    options = series.options,
                    graph = series.graph,
                    stateOptions = options.states,
                    lineWidth = options.lineWidth,
                    attribs,
                    i = 0;

                state = state || '';

                if (series.state !== state) {

                    // Toggle class names
                    each([series.group, series.markerGroup], function(group) {
                        if (group) {
                            // Old state
                            if (series.state) {
                                group.removeClass('highcharts-series-' + series.state);
                            }
                            // New state
                            if (state) {
                                group.addClass('highcharts-series-' + state);
                            }
                        }
                    });

                    series.state = state;



                    if (stateOptions[state] && stateOptions[state].enabled === false) {
                        return;
                    }

                    if (state) {
                        lineWidth = stateOptions[state].lineWidth || lineWidth + (stateOptions[state].lineWidthPlus || 0); // #4035
                    }

                    if (graph && !graph.dashstyle) { // hover is turned off for dashed lines in VML
                        attribs = {
                            'stroke-width': lineWidth
                        };
                        // use attr because animate will cause any other animation on the graph to stop
                        graph.attr(attribs);
                        while (series['zone-graph-' + i]) {
                            series['zone-graph-' + i].attr(attribs);
                            i = i + 1;
                        }
                    }

                }
            },

            /**
             * Set the visibility of the graph
             *
             * @param vis {Boolean} True to show the series, false to hide. If undefined,
             *				the visibility is toggled.
             */
            setVisible: function(vis, redraw) {
                var series = this,
                    chart = series.chart,
                    legendItem = series.legendItem,
                    showOrHide,
                    ignoreHiddenSeries = chart.options.chart.ignoreHiddenSeries,
                    oldVisibility = series.visible;

                // if called without an argument, toggle visibility
                series.visible = vis = series.options.visible = series.userOptions.visible = vis === undefined ? !oldVisibility : vis; // #5618
                showOrHide = vis ? 'show' : 'hide';

                // show or hide elements
                each(['group', 'dataLabelsGroup', 'markerGroup', 'tracker', 'tt'], function(key) {
                    if (series[key]) {
                        series[key][showOrHide]();
                    }
                });


                // hide tooltip (#1361)
                if (chart.hoverSeries === series || (chart.hoverPoint && chart.hoverPoint.series) === series) {
                    series.onMouseOut();
                }


                if (legendItem) {
                    chart.legend.colorizeItem(series, vis);
                }


                // rescale or adapt to resized chart
                series.isDirty = true;
                // in a stack, all other series are affected
                if (series.options.stacking) {
                    each(chart.series, function(otherSeries) {
                        if (otherSeries.options.stacking && otherSeries.visible) {
                            otherSeries.isDirty = true;
                        }
                    });
                }

                // show or hide linked series
                each(series.linkedSeries, function(otherSeries) {
                    otherSeries.setVisible(vis, false);
                });

                if (ignoreHiddenSeries) {
                    chart.isDirtyBox = true;
                }
                if (redraw !== false) {
                    chart.redraw();
                }

                fireEvent(series, showOrHide);
            },

            /**
             * Show the graph
             */
            show: function() {
                this.setVisible(true);
            },

            /**
             * Hide the graph
             */
            hide: function() {
                this.setVisible(false);
            },


            /**
             * Set the selected state of the graph
             *
             * @param selected {Boolean} True to select the series, false to unselect. If
             *				undefined, the selection state is toggled.
             */
            select: function(selected) {
                var series = this;
                // if called without an argument, toggle
                series.selected = selected = (selected === undefined) ? !series.selected : selected;

                if (series.checkbox) {
                    series.checkbox.checked = selected;
                }

                fireEvent(series, selected ? 'select' : 'unselect');
            },

            drawTracker: TrackerMixin.drawTrackerGraph
        });

    }(Highcharts));
    (function(H) {
        /**
         * (c) 2010-2016 Torstein Honsi
         *
         * License: www.highcharts.com/license
         */
        'use strict';
        var Chart = H.Chart,
            each = H.each,
            inArray = H.inArray,
            isObject = H.isObject,
            pick = H.pick,
            splat = H.splat;

        /**
         * Update the chart based on the current chart/document size and options for responsiveness
         */
        Chart.prototype.setResponsive = function(redraw) {
            var options = this.options.responsive;

            if (options && options.rules) {
                each(options.rules, function(rule) {
                    this.matchResponsiveRule(rule, redraw);
                }, this);
            }
        };

        /**
         * Handle a single responsiveness rule
         */
        Chart.prototype.matchResponsiveRule = function(rule, redraw) {
            var respRules = this.respRules,
                condition = rule.condition,
                matches,
                fn = condition.callback || function() {
                    return this.chartWidth <= pick(condition.maxWidth, Number.MAX_VALUE) &&
                        this.chartHeight <= pick(condition.maxHeight, Number.MAX_VALUE) &&
                        this.chartWidth >= pick(condition.minWidth, 0) &&
                        this.chartHeight >= pick(condition.minHeight, 0);
                };


            if (rule._id === undefined) {
                rule._id = H.uniqueKey();
            }
            matches = fn.call(this);

            // Apply a rule
            if (!respRules[rule._id] && matches) {

                // Store the current state of the options
                if (rule.chartOptions) {
                    respRules[rule._id] = this.currentOptions(rule.chartOptions);
                    this.update(rule.chartOptions, redraw);
                }

                // Unapply a rule based on the previous options before the rule
                // was applied
            } else if (respRules[rule._id] && !matches) {
                this.update(respRules[rule._id], redraw);
                delete respRules[rule._id];
            }
        };

        /**
         * Get the current values for a given set of options. Used before we update
         * the chart with a new responsiveness rule.
         * TODO: Restore axis options (by id?)
         */
        Chart.prototype.currentOptions = function(options) {

            var ret = {};

            /**
             * Recurse over a set of options and its current values,
             * and store the current values in the ret object.
             */
            function getCurrent(options, curr, ret) {
                var key, i;
                for (key in options) {
                    if (inArray(key, ['series', 'xAxis', 'yAxis']) > -1) {
                        options[key] = splat(options[key]);

                        ret[key] = [];
                        for (i = 0; i < options[key].length; i++) {
                            ret[key][i] = {};
                            getCurrent(options[key][i], curr[key][i], ret[key][i]);
                        }
                    } else if (isObject(options[key])) {
                        ret[key] = {};
                        getCurrent(options[key], curr[key] || {}, ret[key]);
                    } else {
                        ret[key] = curr[key] || null;
                    }
                }
            }

            getCurrent(options, this.options, ret);
            return ret;
        };

    }(Highcharts));
    return Highcharts
}));

/**
 * highcharts-ng
 * @version v0.0.12 - 2016-08-07
 * @link https://github.com/pablojim/highcharts-ng
 * @author Barry Fitzgerald <>
 * @license MIT License, http://www.opensource.org/licenses/MIT
 */

if (typeof module !== 'undefined' && typeof exports !== 'undefined' && module.exports === exports){
  module.exports = 'highcharts-ng';
}

(function () {
  'use strict';
  /*global angular: false, Highcharts: false */


  angular.module('highcharts-ng', [])
    .factory('highchartsNG', ['$q', '$window', highchartsNG])
    .directive('highchart', ['highchartsNG', '$timeout', highchart]);

  //IE8 support
  function indexOf(arr, find, i /*opt*/) {
    if (i === undefined) i = 0;
    if (i < 0) i += arr.length;
    if (i < 0) i = 0;
    for (var n = arr.length; i < n; i++)
      if (i in arr && arr[i] === find)
        return i;
    return -1;
  }

  function prependMethod(obj, method, func) {
    var original = obj[method];
    obj[method] = function () {
      var args = Array.prototype.slice.call(arguments);
      func.apply(this, args);
      if (original) {
        return original.apply(this, args);
      } else {
        return;
      }

    };
  }

  function deepExtend(destination, source) {
    //Slightly strange behaviour in edge cases (e.g. passing in non objects)
    //But does the job for current use cases.
    if (angular.isArray(source)) {
      destination = angular.isArray(destination) ? destination : [];
      for (var i = 0; i < source.length; i++) {
        destination[i] = deepExtend(destination[i] || {}, source[i]);
      }
    } else if (angular.isObject(source)) {
      destination = angular.isObject(destination) ? destination : {};
      for (var property in source) {
        destination[property] = deepExtend(destination[property] || {}, source[property]);
      }
    } else {
      destination = source;
    }
    return destination;
  }

  function highchartsNG($q, $window) {
    var highchartsProm = $q.when($window.Highcharts);

    function getHighchartsOnce() {
      return highchartsProm;
    }

    return {
      getHighcharts: getHighchartsOnce,
      ready: function ready(callback, thisArg) {
        getHighchartsOnce().then(function() {
          callback.call(thisArg);
        });
      }
    };
  }

  function highchart(highchartsNGUtils, $timeout) {

    // acceptable shared state
    var seriesId = 0;
    var ensureIds = function (series) {
      var changed = false;
      angular.forEach(series, function(s) {
        if (!angular.isDefined(s.id)) {
          s.id = 'series-' + seriesId++;
          changed = true;
        }
      });
      return changed;
    };

    // immutable
    var axisNames = [ 'xAxis', 'yAxis' ];
    var chartTypeMap = {
      'stock': 'StockChart',
      'map':   'Map',
      'chart': 'Chart'
    };

    var getMergedOptions = function (scope, element, config) {
      var mergedOptions = {};

      var defaultOptions = {
        chart: {
          events: {}
        },
        title: {},
        subtitle: {},
        series: [],
        credits: {},
        plotOptions: {},
        navigator: {enabled: false},
        xAxis: {
          events: {}
        },
        yAxis: {
          events: {}
        }
      };

      if (config.options) {
        mergedOptions = deepExtend(defaultOptions, config.options);
      } else {
        mergedOptions = defaultOptions;
      }
      mergedOptions.chart.renderTo = element[0];

      angular.forEach(axisNames, function(axisName) {
        if(angular.isDefined(config[axisName])) {
          mergedOptions[axisName] = deepExtend(mergedOptions[axisName] || {}, config[axisName]);

          if(angular.isDefined(config[axisName].currentMin) ||
              angular.isDefined(config[axisName].currentMax)) {

            prependMethod(mergedOptions.chart.events, 'selection', function(e){
              var thisChart = this;
              if (e[axisName]) {
                scope.$apply(function () {
                  scope.config[axisName].currentMin = e[axisName][0].min;
                  scope.config[axisName].currentMax = e[axisName][0].max;
                });
              } else {
                //handle reset button - zoom out to all
                scope.$apply(function () {
                  scope.config[axisName].currentMin = thisChart[axisName][0].dataMin;
                  scope.config[axisName].currentMax = thisChart[axisName][0].dataMax;
                });
              }
            });

            prependMethod(mergedOptions.chart.events, 'addSeries', function(e){
              scope.config[axisName].currentMin = this[axisName][0].min || scope.config[axisName].currentMin;
              scope.config[axisName].currentMax = this[axisName][0].max || scope.config[axisName].currentMax;
            });
            prependMethod(mergedOptions[axisName].events, 'setExtremes', function (e) {
              if (e.trigger && e.trigger !== 'zoom') { // zoom trigger is handled by selection event
                $timeout(function () {
                  scope.config[axisName].currentMin = e.min;
                  scope.config[axisName].currentMax = e.max;
                  scope.config[axisName].min = e.min; // set min and max to adjust scrollbar/navigator
                  scope.config[axisName].max = e.max;
                }, 0);
              }
            });
          }
        }
      });

      if(config.title) {
        mergedOptions.title = config.title;
      }
      if (config.subtitle) {
        mergedOptions.subtitle = config.subtitle;
      }
      if (config.credits) {
        mergedOptions.credits = config.credits;
      }
      if(config.size) {
        if (config.size.width) {
          mergedOptions.chart.width = config.size.width;
        }
        if (config.size.height) {
          mergedOptions.chart.height = config.size.height;
        }
      }
      return mergedOptions;
    };

    var updateZoom = function (axis, modelAxis) {
      var extremes = axis.getExtremes();
      if(modelAxis.currentMin !== extremes.dataMin || modelAxis.currentMax !== extremes.dataMax) {
        if (axis.setExtremes) {
          axis.setExtremes(modelAxis.currentMin, modelAxis.currentMax, false);
        } else {
          axis.detachedsetExtremes(modelAxis.currentMin, modelAxis.currentMax, false);
        }
      }
    };

    var processExtremes = function(chart, axis, axisName) {
      if(axis.currentMin || axis.currentMax) {
        chart[axisName][0].setExtremes(axis.currentMin, axis.currentMax, true);
      }
    };

    var chartOptionsWithoutEasyOptions = function (options) {
      return angular.extend(
        deepExtend({}, options),
        { data: null, visible: null }
      );
    };

    var getChartType = function(scope) {
      if (scope.config === undefined) return 'Chart';
      return chartTypeMap[('' + scope.config.chartType).toLowerCase()] ||
             (scope.config.useHighStocks ? 'StockChart' : 'Chart');
    };

    function linkWithHighcharts(Highcharts, scope, element, attrs) {
      // We keep some chart-specific variables here as a closure
      // instead of storing them on 'scope'.

      // prevSeriesOptions is maintained by processSeries
      var prevSeriesOptions = {};
      // chart is maintained by initChart
      var chart = false;

      var processSeries = function(series, seriesOld) {
        var i;
        var ids = [];

        if(series) {
          var setIds = ensureIds(series);
          if(setIds && !scope.disableDataWatch) {
            //If we have set some ids this will trigger another digest cycle.
            //In this scenario just return early and let the next cycle take care of changes
            return false;
          }

          //Find series to add or update
          angular.forEach(series, function(s, idx) {
            ids.push(s.id);
            var chartSeries = chart.get(s.id);
            if (chartSeries) {
              if (!angular.equals(prevSeriesOptions[s.id], chartOptionsWithoutEasyOptions(s))) {
                chartSeries.update(angular.copy(s), false);
              } else {
                if (s.visible !== undefined && chartSeries.visible !== s.visible) {
                  chartSeries.setVisible(s.visible, false);
                }
                
                // Make sure the current series index can be accessed in seriesOld
                if (idx < seriesOld.length) {
                  var sOld = seriesOld[idx];
                  var sCopy = angular.copy(sOld);
                  
                  // Get the latest data point from the new series
                  var ptNew = s.data[s.data.length - 1];
                  
                  // Check if the new and old series are identical with the latest data point added
                  // If so, call addPoint without shifting
                  sCopy.data.push(ptNew);
                  if (angular.equals(sCopy, s)) {
                    chartSeries.addPoint(ptNew, false);
                  }
                  
                  // Check if the data change was a push and shift operation
                  // If so, call addPoint WITH shifting
                  else {
                    sCopy.data.shift();
                    if (angular.equals(sCopy, s)) {
                      chartSeries.addPoint(ptNew, false, true);
                    }
                    else {
                      chartSeries.setData(angular.copy(s.data), false);
                    }
                  }
                }
                else {
                  chartSeries.setData(angular.copy(s.data), false);
                }
              }
            } else {
              chart.addSeries(angular.copy(s), false);
            }
            prevSeriesOptions[s.id] = chartOptionsWithoutEasyOptions(s);
          });

          //  Shows no data text if all series are empty
          if(scope.config.noData) {
            var chartContainsData = false;

            for(i = 0; i < series.length; i++) {
              if (series[i].data && series[i].data.length > 0) {
                chartContainsData = true;

                break;
              }
            }

            if (!chartContainsData) {
              chart.showLoading(scope.config.noData);
            } else {
              chart.hideLoading();
            }
          }
        }

        //Now remove any missing series
        for(i = chart.series.length - 1; i >= 0; i--) {
          var s = chart.series[i];
          if (s.options.id !== 'highcharts-navigator-series' && indexOf(ids, s.options.id) < 0) {
            s.remove(false);
          }
        }

        return true;
      };

      var initChart = function() {
        if (chart) chart.destroy();
        prevSeriesOptions = {};
        var config = scope.config || {};
        var mergedOptions = getMergedOptions(scope, element, config);
        var func = config.func || undefined;
        var chartType = getChartType(scope);

        chart = new Highcharts[chartType](mergedOptions, func);

        for (var i = 0; i < axisNames.length; i++) {
          if (config[axisNames[i]]) {
            processExtremes(chart, config[axisNames[i]], axisNames[i]);
          }
        }
        if(config.loading) {
          chart.showLoading();
        }
        config.getHighcharts = function() {
          return chart;
        };

      };
      initChart();


      if(scope.disableDataWatch){
        scope.$watchCollection('config.series', function (newSeries, oldSeries) {
          processSeries(newSeries);
          chart.redraw();
        });
      } else {
        scope.$watch('config.series', function (newSeries, oldSeries) {
          var needsRedraw = processSeries(newSeries, oldSeries);
          if(needsRedraw) {
            chart.redraw();
          }
        }, true);
      }

      scope.$watch('config.title', function (newTitle) {
        chart.setTitle(newTitle, true);
      }, true);

      scope.$watch('config.subtitle', function (newSubtitle) {
        chart.setTitle(true, newSubtitle);
      }, true);

      scope.$watch('config.loading', function (loading) {
        if(loading) {
          chart.showLoading(loading === true ? null : loading);
        } else {
          chart.hideLoading();
        }
      });
      scope.$watch('config.noData', function (noData) {
        if(scope.config && scope.config.loading) {
          chart.showLoading(noData);
        }
      }, true);

      scope.$watch('config.credits.enabled', function (enabled) {
        if (enabled) {
          chart.credits.show();
        } else if (chart.credits) {
          chart.credits.hide();
        }
      });

      scope.$watch(getChartType, function (chartType, oldChartType) {
        if (chartType === oldChartType) return;
        initChart();
      });

      angular.forEach(axisNames, function(axisName) {
        scope.$watch('config.' + axisName, function(newAxes) {
          if (!newAxes) {
            return;
          }

          if (angular.isArray(newAxes)) {

            for (var axisIndex = 0; axisIndex < newAxes.length; axisIndex++) {
              var axis = newAxes[axisIndex];

              if (axisIndex < chart[axisName].length) {
                chart[axisName][axisIndex].update(axis, false);
                updateZoom(chart[axisName][axisIndex], angular.copy(axis));
              }

            }

          } else {
            // update single axis
            chart[axisName][0].update(newAxes, false);
            updateZoom(chart[axisName][0], angular.copy(newAxes));
          }

          chart.redraw();
        }, true);
      });
      scope.$watch('config.options', function (newOptions, oldOptions, scope) {
        //do nothing when called on registration
        if (newOptions === oldOptions) return;
        initChart();
        processSeries(scope.config.series);
        chart.redraw();
      }, true);

      scope.$watch('config.size', function (newSize, oldSize) {
        if(newSize === oldSize) return;
        if(newSize) {
          chart.setSize(newSize.width || chart.chartWidth, newSize.height || chart.chartHeight);
        }
      }, true);

      scope.$on('highchartsng.reflow', function () {
        chart.reflow();
      });

      scope.$on('$destroy', function() {
        if (chart) {
          try{
            chart.destroy();
          }catch(ex){
            // fail silently as highcharts will throw exception if element doesn't exist
          }

          $timeout(function(){
            element.remove();
          }, 0);
        }
      });
    }

    function link(scope, element, attrs) {
      function highchartsCb(Highcarts) {
        linkWithHighcharts(Highcharts, scope, element, attrs);
      }
      highchartsNGUtils
        .getHighcharts()
        .then(highchartsCb);
    }

    return {
      restrict: 'EAC',
      replace: true,
      template: '<div></div>',
      scope: {
        config: '=',
        disableDataWatch: '='
      },
      link: link
    };
  }
}());

(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
  typeof define === 'function' && define.amd ? define(factory) :
  (global.jspdf = factory());
}(this, function () { 'use strict';

  var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && typeof Symbol === "function" && obj.constructor === Symbol ? "symbol" : typeof obj;
  };

  /** @preserve
   * jsPDF - PDF Document creation from JavaScript
   * Version 1.3.0 Built on 2016-09-28T10:52:40.410Z
   *                           CommitID a434267220
   *
   * Copyright (c) 2010-2014 James Hall <james@parall.ax>, https://github.com/MrRio/jsPDF
   *               2010 Aaron Spike, https://github.com/acspike
   *               2012 Willow Systems Corporation, willow-systems.com
   *               2012 Pablo Hess, https://github.com/pablohess
   *               2012 Florian Jenett, https://github.com/fjenett
   *               2013 Warren Weckesser, https://github.com/warrenweckesser
   *               2013 Youssef Beddad, https://github.com/lifof
   *               2013 Lee Driscoll, https://github.com/lsdriscoll
   *               2013 Stefan Slonevskiy, https://github.com/stefslon
   *               2013 Jeremy Morel, https://github.com/jmorel
   *               2013 Christoph Hartmann, https://github.com/chris-rock
   *               2014 Juan Pablo Gaviria, https://github.com/juanpgaviria
   *               2014 James Makes, https://github.com/dollaruw
   *               2014 Diego Casorran, https://github.com/diegocr
   *               2014 Steven Spungin, https://github.com/Flamenco
   *               2014 Kenneth Glassey, https://github.com/Gavvers
   *
   * Licensed under the MIT License
   *
   * Contributor(s):
   *    siefkenj, ahwolf, rickygu, Midnith, saintclair, eaparango,
   *    kim3er, mfo, alnorth, Flamenco
   */

  /**
   * Creates new jsPDF document object instance.
   *
   * @class
   * @param orientation One of "portrait" or "landscape" (or shortcuts "p" (Default), "l")
   * @param unit        Measurement unit to be used when coordinates are specified.
   *                    One of "pt" (points), "mm" (Default), "cm", "in"
   * @param format      One of 'pageFormats' as shown below, default: a4
   * @returns {jsPDF}
   * @name jsPDF
   */
  var jsPDF = function (global) {
      'use strict';

      var pdfVersion = '1.3',
          pageFormats = { // Size in pt of various paper formats
          'a0': [2383.94, 3370.39], 'a1': [1683.78, 2383.94],
          'a2': [1190.55, 1683.78], 'a3': [841.89, 1190.55],
          'a4': [595.28, 841.89], 'a5': [419.53, 595.28],
          'a6': [297.64, 419.53], 'a7': [209.76, 297.64],
          'a8': [147.40, 209.76], 'a9': [104.88, 147.40],
          'a10': [73.70, 104.88], 'b0': [2834.65, 4008.19],
          'b1': [2004.09, 2834.65], 'b2': [1417.32, 2004.09],
          'b3': [1000.63, 1417.32], 'b4': [708.66, 1000.63],
          'b5': [498.90, 708.66], 'b6': [354.33, 498.90],
          'b7': [249.45, 354.33], 'b8': [175.75, 249.45],
          'b9': [124.72, 175.75], 'b10': [87.87, 124.72],
          'c0': [2599.37, 3676.54], 'c1': [1836.85, 2599.37],
          'c2': [1298.27, 1836.85], 'c3': [918.43, 1298.27],
          'c4': [649.13, 918.43], 'c5': [459.21, 649.13],
          'c6': [323.15, 459.21], 'c7': [229.61, 323.15],
          'c8': [161.57, 229.61], 'c9': [113.39, 161.57],
          'c10': [79.37, 113.39], 'dl': [311.81, 623.62],
          'letter': [612, 792],
          'government-letter': [576, 756],
          'legal': [612, 1008],
          'junior-legal': [576, 360],
          'ledger': [1224, 792],
          'tabloid': [792, 1224],
          'credit-card': [153, 243]
      };

      /**
       * jsPDF's Internal PubSub Implementation.
       * See mrrio.github.io/jsPDF/doc/symbols/PubSub.html
       * Backward compatible rewritten on 2014 by
       * Diego Casorran, https://github.com/diegocr
       *
       * @class
       * @name PubSub
       */
      function PubSub(context) {
          var topics = {};

          this.subscribe = function (topic, callback, once) {
              if (typeof callback !== 'function') {
                  return false;
              }

              if (!topics.hasOwnProperty(topic)) {
                  topics[topic] = {};
              }

              var id = Math.random().toString(35);
              topics[topic][id] = [callback, !!once];

              return id;
          };

          this.unsubscribe = function (token) {
              for (var topic in topics) {
                  if (topics[topic][token]) {
                      delete topics[topic][token];
                      return true;
                  }
              }
              return false;
          };

          this.publish = function (topic) {
              if (topics.hasOwnProperty(topic)) {
                  var args = Array.prototype.slice.call(arguments, 1),
                      idr = [];

                  for (var id in topics[topic]) {
                      var sub = topics[topic][id];
                      try {
                          sub[0].apply(context, args);
                      } catch (ex) {
                          if (global.console) {
                              console.error('jsPDF PubSub Error', ex.message, ex);
                          }
                      }
                      if (sub[1]) idr.push(id);
                  }
                  if (idr.length) idr.forEach(this.unsubscribe);
              }
          };
      }

      /**
       * @constructor
       * @private
       */
      function jsPDF(orientation, unit, format, compressPdf) {
          var options = {};

          if ((typeof orientation === 'undefined' ? 'undefined' : _typeof(orientation)) === 'object') {
              options = orientation;

              orientation = options.orientation;
              unit = options.unit || unit;
              format = options.format || format;
              compressPdf = options.compress || options.compressPdf || compressPdf;
          }

          // Default options
          unit = unit || 'mm';
          format = format || 'a4';
          orientation = ('' + (orientation || 'P')).toLowerCase();

          var format_as_string = ('' + format).toLowerCase(),
              compress = !!compressPdf && typeof Uint8Array === 'function',
              textColor = options.textColor || '0 g',
              drawColor = options.drawColor || '0 G',
              activeFontSize = options.fontSize || 16,
              lineHeightProportion = options.lineHeight || 1.15,
              lineWidth = options.lineWidth || 0.200025,
              // 2mm
          objectNumber = 2,
              // 'n' Current object number
          outToPages = !1,
              // switches where out() prints. outToPages true = push to pages obj. outToPages false = doc builder content
          offsets = [],
              // List of offsets. Activated and reset by buildDocument(). Pupulated by various calls buildDocument makes.
          fonts = {},
              // collection of font objects, where key is fontKey - a dynamically created label for a given font.
          fontmap = {},
              // mapping structure fontName > fontStyle > font key - performance layer. See addFont()
          activeFontKey,
              // will be string representing the KEY of the font as combination of fontName + fontStyle
          k,
              // Scale factor
          tmp,
              page = 0,
              currentPage,
              pages = [],
              pagesContext = [],
              // same index as pages and pagedim
          pagedim = [],
              content = [],
              additionalObjects = [],
              lineCapID = 0,
              lineJoinID = 0,
              content_length = 0,
              pageWidth,
              pageHeight,
              pageMode,
              zoomMode,
              layoutMode,
              documentProperties = {
              'title': '',
              'subject': '',
              'author': '',
              'keywords': '',
              'creator': ''
          },
              API = {},
              events = new PubSub(API),


          /////////////////////
          // Private functions
          /////////////////////
          f2 = function f2(number) {
              return number.toFixed(2); // Ie, %.2f
          },
              f3 = function f3(number) {
              return number.toFixed(3); // Ie, %.3f
          },
              padd2 = function padd2(number) {
              return ('0' + parseInt(number)).slice(-2);
          },
              out = function out(string) {
              if (outToPages) {
                  /* set by beginPage */
                  pages[currentPage].push(string);
              } else {
                  // +1 for '\n' that will be used to join 'content'
                  content_length += string.length + 1;
                  content.push(string);
              }
          },
              newObject = function newObject() {
              // Begin a new object
              objectNumber++;
              offsets[objectNumber] = content_length;
              out(objectNumber + ' 0 obj');
              return objectNumber;
          },

          // Does not output the object until after the pages have been output.
          // Returns an object containing the objectId and content.
          // All pages have been added so the object ID can be estimated to start right after.
          // This does not modify the current objectNumber;  It must be updated after the newObjects are output.
          newAdditionalObject = function newAdditionalObject() {
              var objId = pages.length * 2 + 1;
              objId += additionalObjects.length;
              var obj = { objId: objId, content: '' };
              additionalObjects.push(obj);
              return obj;
          },

          // Does not output the object.  The caller must call newObjectDeferredBegin(oid) before outputing any data
          newObjectDeferred = function newObjectDeferred() {
              objectNumber++;
              offsets[objectNumber] = function () {
                  return content_length;
              };
              return objectNumber;
          },
              newObjectDeferredBegin = function newObjectDeferredBegin(oid) {
              offsets[oid] = content_length;
          },
              putStream = function putStream(str) {
              out('stream');
              out(str);
              out('endstream');
          },
              putPages = function putPages() {
              var n,
                  p,
                  arr,
                  i,
                  deflater,
                  adler32,
                  adler32cs,
                  wPt,
                  hPt,
                  pageObjectNumbers = [];

              adler32cs = global.adler32cs || jsPDF.adler32cs;
              if (compress && typeof adler32cs === 'undefined') {
                  compress = false;
              }

              // outToPages = false as set in endDocument(). out() writes to content.

              for (n = 1; n <= page; n++) {
                  pageObjectNumbers.push(newObject());
                  wPt = (pageWidth = pagedim[n].width) * k;
                  hPt = (pageHeight = pagedim[n].height) * k;
                  out('<</Type /Page');
                  out('/Parent 1 0 R');
                  out('/Resources 2 0 R');
                  out('/MediaBox [0 0 ' + f2(wPt) + ' ' + f2(hPt) + ']');
                  // Added for annotation plugin
                  events.publish('putPage', { pageNumber: n, page: pages[n] });
                  out('/Contents ' + (objectNumber + 1) + ' 0 R');
                  out('>>');
                  out('endobj');

                  // Page content
                  p = pages[n].join('\n');
                  newObject();
                  if (compress) {
                      arr = [];
                      i = p.length;
                      while (i--) {
                          arr[i] = p.charCodeAt(i);
                      }
                      adler32 = adler32cs.from(p);
                      deflater = new Deflater(6);
                      deflater.append(new Uint8Array(arr));
                      p = deflater.flush();
                      arr = new Uint8Array(p.length + 6);
                      arr.set(new Uint8Array([120, 156])), arr.set(p, 2);
                      arr.set(new Uint8Array([adler32 & 0xFF, adler32 >> 8 & 0xFF, adler32 >> 16 & 0xFF, adler32 >> 24 & 0xFF]), p.length + 2);
                      p = String.fromCharCode.apply(null, arr);
                      out('<</Length ' + p.length + ' /Filter [/FlateDecode]>>');
                  } else {
                      out('<</Length ' + p.length + '>>');
                  }
                  putStream(p);
                  out('endobj');
              }
              offsets[1] = content_length;
              out('1 0 obj');
              out('<</Type /Pages');
              var kids = '/Kids [';
              for (i = 0; i < page; i++) {
                  kids += pageObjectNumbers[i] + ' 0 R ';
              }
              out(kids + ']');
              out('/Count ' + page);
              out('>>');
              out('endobj');
              events.publish('postPutPages');
          },
              putFont = function putFont(font) {
              font.objectNumber = newObject();
              out('<</BaseFont/' + font.PostScriptName + '/Type/Font');
              if (typeof font.encoding === 'string') {
                  out('/Encoding/' + font.encoding);
              }
              out('/Subtype/Type1>>');
              out('endobj');
          },
              putFonts = function putFonts() {
              for (var fontKey in fonts) {
                  if (fonts.hasOwnProperty(fontKey)) {
                      putFont(fonts[fontKey]);
                  }
              }
          },
              putXobjectDict = function putXobjectDict() {
              // Loop through images, or other data objects
              events.publish('putXobjectDict');
          },
              putResourceDictionary = function putResourceDictionary() {
              out('/ProcSet [/PDF /Text /ImageB /ImageC /ImageI]');
              out('/Font <<');

              // Do this for each font, the '1' bit is the index of the font
              for (var fontKey in fonts) {
                  if (fonts.hasOwnProperty(fontKey)) {
                      out('/' + fontKey + ' ' + fonts[fontKey].objectNumber + ' 0 R');
                  }
              }
              out('>>');
              out('/XObject <<');
              putXobjectDict();
              out('>>');
          },
              putResources = function putResources() {
              putFonts();
              events.publish('putResources');
              // Resource dictionary
              offsets[2] = content_length;
              out('2 0 obj');
              out('<<');
              putResourceDictionary();
              out('>>');
              out('endobj');
              events.publish('postPutResources');
          },
              putAdditionalObjects = function putAdditionalObjects() {
              events.publish('putAdditionalObjects');
              for (var i = 0; i < additionalObjects.length; i++) {
                  var obj = additionalObjects[i];
                  offsets[obj.objId] = content_length;
                  out(obj.objId + ' 0 obj');
                  out(obj.content);
                  ;
                  out('endobj');
              }
              objectNumber += additionalObjects.length;
              events.publish('postPutAdditionalObjects');
          },
              addToFontDictionary = function addToFontDictionary(fontKey, fontName, fontStyle) {
              // this is mapping structure for quick font key lookup.
              // returns the KEY of the font (ex: "F1") for a given
              // pair of font name and type (ex: "Arial". "Italic")
              if (!fontmap.hasOwnProperty(fontName)) {
                  fontmap[fontName] = {};
              }
              fontmap[fontName][fontStyle] = fontKey;
          },

          /**
           * FontObject describes a particular font as member of an instnace of jsPDF
           *
           * It's a collection of properties like 'id' (to be used in PDF stream),
           * 'fontName' (font's family name), 'fontStyle' (font's style variant label)
           *
           * @class
           * @public
           * @property id {String} PDF-document-instance-specific label assinged to the font.
           * @property PostScriptName {String} PDF specification full name for the font
           * @property encoding {Object} Encoding_name-to-Font_metrics_object mapping.
           * @name FontObject
           */
          addFont = function addFont(PostScriptName, fontName, fontStyle, encoding) {
              var fontKey = 'F' + (Object.keys(fonts).length + 1).toString(10),

              // This is FontObject
              font = fonts[fontKey] = {
                  'id': fontKey,
                  'PostScriptName': PostScriptName,
                  'fontName': fontName,
                  'fontStyle': fontStyle,
                  'encoding': encoding,
                  'metadata': {}
              };
              addToFontDictionary(fontKey, fontName, fontStyle);
              events.publish('addFont', font);

              return fontKey;
          },
              addFonts = function addFonts() {

              var HELVETICA = "helvetica",
                  TIMES = "times",
                  COURIER = "courier",
                  NORMAL = "normal",
                  BOLD = "bold",
                  ITALIC = "italic",
                  BOLD_ITALIC = "bolditalic",
                  encoding = 'StandardEncoding',
                  ZAPF = "zapfdingbats",
                  standardFonts = [['Helvetica', HELVETICA, NORMAL], ['Helvetica-Bold', HELVETICA, BOLD], ['Helvetica-Oblique', HELVETICA, ITALIC], ['Helvetica-BoldOblique', HELVETICA, BOLD_ITALIC], ['Courier', COURIER, NORMAL], ['Courier-Bold', COURIER, BOLD], ['Courier-Oblique', COURIER, ITALIC], ['Courier-BoldOblique', COURIER, BOLD_ITALIC], ['Times-Roman', TIMES, NORMAL], ['Times-Bold', TIMES, BOLD], ['Times-Italic', TIMES, ITALIC], ['Times-BoldItalic', TIMES, BOLD_ITALIC], ['ZapfDingbats', ZAPF]];

              for (var i = 0, l = standardFonts.length; i < l; i++) {
                  var fontKey = addFont(standardFonts[i][0], standardFonts[i][1], standardFonts[i][2], encoding);

                  // adding aliases for standard fonts, this time matching the capitalization
                  var parts = standardFonts[i][0].split('-');
                  addToFontDictionary(fontKey, parts[0], parts[1] || '');
              }
              events.publish('addFonts', { fonts: fonts, dictionary: fontmap });
          },
              SAFE = function __safeCall(fn) {
              fn.foo = function __safeCallWrapper() {
                  try {
                      return fn.apply(this, arguments);
                  } catch (e) {
                      var stack = e.stack || '';
                      if (~stack.indexOf(' at ')) stack = stack.split(" at ")[1];
                      var m = "Error in function " + stack.split("\n")[0].split('<')[0] + ": " + e.message;
                      if (global.console) {
                          global.console.error(m, e);
                          if (global.alert) alert(m);
                      } else {
                          throw new Error(m);
                      }
                  }
              };
              fn.foo.bar = fn;
              return fn.foo;
          },
              to8bitStream = function to8bitStream(text, flags) {
              /**
               * PDF 1.3 spec:
               * "For text strings encoded in Unicode, the first two bytes must be 254 followed by
               * 255, representing the Unicode byte order marker, U+FEFF. (This sequence conflicts
               * with the PDFDocEncoding character sequence thorn ydieresis, which is unlikely
               * to be a meaningful beginning of a word or phrase.) The remainder of the
               * string consists of Unicode character codes, according to the UTF-16 encoding
               * specified in the Unicode standard, version 2.0. Commonly used Unicode values
               * are represented as 2 bytes per character, with the high-order byte appearing first
               * in the string."
               *
               * In other words, if there are chars in a string with char code above 255, we
               * recode the string to UCS2 BE - string doubles in length and BOM is prepended.
               *
               * HOWEVER!
               * Actual *content* (body) text (as opposed to strings used in document properties etc)
               * does NOT expect BOM. There, it is treated as a literal GID (Glyph ID)
               *
               * Because of Adobe's focus on "you subset your fonts!" you are not supposed to have
               * a font that maps directly Unicode (UCS2 / UTF16BE) code to font GID, but you could
               * fudge it with "Identity-H" encoding and custom CIDtoGID map that mimics Unicode
               * code page. There, however, all characters in the stream are treated as GIDs,
               * including BOM, which is the reason we need to skip BOM in content text (i.e. that
               * that is tied to a font).
               *
               * To signal this "special" PDFEscape / to8bitStream handling mode,
               * API.text() function sets (unless you overwrite it with manual values
               * given to API.text(.., flags) )
               * flags.autoencode = true
               * flags.noBOM = true
               *
               * ===================================================================================
               * `flags` properties relied upon:
               *   .sourceEncoding = string with encoding label.
               *                     "Unicode" by default. = encoding of the incoming text.
               *                     pass some non-existing encoding name
               *                     (ex: 'Do not touch my strings! I know what I am doing.')
               *                     to make encoding code skip the encoding step.
               *   .outputEncoding = Either valid PDF encoding name
               *                     (must be supported by jsPDF font metrics, otherwise no encoding)
               *                     or a JS object, where key = sourceCharCode, value = outputCharCode
               *                     missing keys will be treated as: sourceCharCode === outputCharCode
               *   .noBOM
               *       See comment higher above for explanation for why this is important
               *   .autoencode
               *       See comment higher above for explanation for why this is important
               */

              var i, l, sourceEncoding, encodingBlock, outputEncoding, newtext, isUnicode, ch, bch;

              flags = flags || {};
              sourceEncoding = flags.sourceEncoding || 'Unicode';
              outputEncoding = flags.outputEncoding;

              // This 'encoding' section relies on font metrics format
              // attached to font objects by, among others,
              // "Willow Systems' standard_font_metrics plugin"
              // see jspdf.plugin.standard_font_metrics.js for format
              // of the font.metadata.encoding Object.
              // It should be something like
              //   .encoding = {'codePages':['WinANSI....'], 'WinANSI...':{code:code, ...}}
              //   .widths = {0:width, code:width, ..., 'fof':divisor}
              //   .kerning = {code:{previous_char_code:shift, ..., 'fof':-divisor},...}
              if ((flags.autoencode || outputEncoding) && fonts[activeFontKey].metadata && fonts[activeFontKey].metadata[sourceEncoding] && fonts[activeFontKey].metadata[sourceEncoding].encoding) {
                  encodingBlock = fonts[activeFontKey].metadata[sourceEncoding].encoding;

                  // each font has default encoding. Some have it clearly defined.
                  if (!outputEncoding && fonts[activeFontKey].encoding) {
                      outputEncoding = fonts[activeFontKey].encoding;
                  }

                  // Hmmm, the above did not work? Let's try again, in different place.
                  if (!outputEncoding && encodingBlock.codePages) {
                      outputEncoding = encodingBlock.codePages[0]; // let's say, first one is the default
                  }

                  if (typeof outputEncoding === 'string') {
                      outputEncoding = encodingBlock[outputEncoding];
                  }
                  // we want output encoding to be a JS Object, where
                  // key = sourceEncoding's character code and
                  // value = outputEncoding's character code.
                  if (outputEncoding) {
                      isUnicode = false;
                      newtext = [];
                      for (i = 0, l = text.length; i < l; i++) {
                          ch = outputEncoding[text.charCodeAt(i)];
                          if (ch) {
                              newtext.push(String.fromCharCode(ch));
                          } else {
                              newtext.push(text[i]);
                          }

                          // since we are looping over chars anyway, might as well
                          // check for residual unicodeness
                          if (newtext[i].charCodeAt(0) >> 8) {
                              /* more than 255 */
                              isUnicode = true;
                          }
                      }
                      text = newtext.join('');
                  }
              }

              i = text.length;
              // isUnicode may be set to false above. Hence the triple-equal to undefined
              while (isUnicode === undefined && i !== 0) {
                  if (text.charCodeAt(i - 1) >> 8) {
                      /* more than 255 */
                      isUnicode = true;
                  }
                  i--;
              }
              if (!isUnicode) {
                  return text;
              }

              newtext = flags.noBOM ? [] : [254, 255];
              for (i = 0, l = text.length; i < l; i++) {
                  ch = text.charCodeAt(i);
                  bch = ch >> 8; // divide by 256
                  if (bch >> 8) {
                      /* something left after dividing by 256 second time */
                      throw new Error("Character at position " + i + " of string '" + text + "' exceeds 16bits. Cannot be encoded into UCS-2 BE");
                  }
                  newtext.push(bch);
                  newtext.push(ch - (bch << 8));
              }
              return String.fromCharCode.apply(undefined, newtext);
          },
              pdfEscape = function pdfEscape(text, flags) {
              /**
               * Replace '/', '(', and ')' with pdf-safe versions
               *
               * Doing to8bitStream does NOT make this PDF display unicode text. For that
               * we also need to reference a unicode font and embed it - royal pain in the rear.
               *
               * There is still a benefit to to8bitStream - PDF simply cannot handle 16bit chars,
               * which JavaScript Strings are happy to provide. So, while we still cannot display
               * 2-byte characters property, at least CONDITIONALLY converting (entire string containing)
               * 16bit chars to (USC-2-BE) 2-bytes per char + BOM streams we ensure that entire PDF
               * is still parseable.
               * This will allow immediate support for unicode in document properties strings.
               */
              return to8bitStream(text, flags).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
          },
              putInfo = function putInfo() {
              out('/Producer (jsPDF ' + jsPDF.version + ')');
              for (var key in documentProperties) {
                  if (documentProperties.hasOwnProperty(key) && documentProperties[key]) {
                      out('/' + key.substr(0, 1).toUpperCase() + key.substr(1) + ' (' + pdfEscape(documentProperties[key]) + ')');
                  }
              }
              var created = new Date(),
                  tzoffset = created.getTimezoneOffset(),
                  tzsign = tzoffset < 0 ? '+' : '-',
                  tzhour = Math.floor(Math.abs(tzoffset / 60)),
                  tzmin = Math.abs(tzoffset % 60),
                  tzstr = [tzsign, padd2(tzhour), "'", padd2(tzmin), "'"].join('');
              out(['/CreationDate (D:', created.getFullYear(), padd2(created.getMonth() + 1), padd2(created.getDate()), padd2(created.getHours()), padd2(created.getMinutes()), padd2(created.getSeconds()), tzstr, ')'].join(''));
          },
              putCatalog = function putCatalog() {
              out('/Type /Catalog');
              out('/Pages 1 0 R');
              // PDF13ref Section 7.2.1
              if (!zoomMode) zoomMode = 'fullwidth';
              switch (zoomMode) {
                  case 'fullwidth':
                      out('/OpenAction [3 0 R /FitH null]');
                      break;
                  case 'fullheight':
                      out('/OpenAction [3 0 R /FitV null]');
                      break;
                  case 'fullpage':
                      out('/OpenAction [3 0 R /Fit]');
                      break;
                  case 'original':
                      out('/OpenAction [3 0 R /XYZ null null 1]');
                      break;
                  default:
                      var pcn = '' + zoomMode;
                      if (pcn.substr(pcn.length - 1) === '%') zoomMode = parseInt(zoomMode) / 100;
                      if (typeof zoomMode === 'number') {
                          out('/OpenAction [3 0 R /XYZ null null ' + f2(zoomMode) + ']');
                      }
              }
              if (!layoutMode) layoutMode = 'continuous';
              switch (layoutMode) {
                  case 'continuous':
                      out('/PageLayout /OneColumn');
                      break;
                  case 'single':
                      out('/PageLayout /SinglePage');
                      break;
                  case 'two':
                  case 'twoleft':
                      out('/PageLayout /TwoColumnLeft');
                      break;
                  case 'tworight':
                      out('/PageLayout /TwoColumnRight');
                      break;
              }
              if (pageMode) {
                  /**
                   * A name object specifying how the document should be displayed when opened:
                   * UseNone      : Neither document outline nor thumbnail images visible -- DEFAULT
                   * UseOutlines  : Document outline visible
                   * UseThumbs    : Thumbnail images visible
                   * FullScreen   : Full-screen mode, with no menu bar, window controls, or any other window visible
                   */
                  out('/PageMode /' + pageMode);
              }
              events.publish('putCatalog');
          },
              putTrailer = function putTrailer() {
              out('/Size ' + (objectNumber + 1));
              out('/Root ' + objectNumber + ' 0 R');
              out('/Info ' + (objectNumber - 1) + ' 0 R');
          },
              beginPage = function beginPage(width, height) {
              // Dimensions are stored as user units and converted to points on output
              var orientation = typeof height === 'string' && height.toLowerCase();
              if (typeof width === 'string') {
                  var format = width.toLowerCase();
                  if (pageFormats.hasOwnProperty(format)) {
                      width = pageFormats[format][0] / k;
                      height = pageFormats[format][1] / k;
                  }
              }
              if (Array.isArray(width)) {
                  height = width[1];
                  width = width[0];
              }
              if (orientation) {
                  switch (orientation.substr(0, 1)) {
                      case 'l':
                          if (height > width) orientation = 's';
                          break;
                      case 'p':
                          if (width > height) orientation = 's';
                          break;
                  }
                  if (orientation === 's') {
                      tmp = width;
                      width = height;
                      height = tmp;
                  }
              }
              outToPages = true;
              pages[++page] = [];
              pagedim[page] = {
                  width: Number(width) || pageWidth,
                  height: Number(height) || pageHeight
              };
              pagesContext[page] = {};
              _setPage(page);
          },
              _addPage = function _addPage() {
              beginPage.apply(this, arguments);
              // Set line width
              out(f2(lineWidth * k) + ' w');
              // Set draw color
              out(drawColor);
              // resurrecting non-default line caps, joins
              if (lineCapID !== 0) {
                  out(lineCapID + ' J');
              }
              if (lineJoinID !== 0) {
                  out(lineJoinID + ' j');
              }
              events.publish('addPage', { pageNumber: page });
          },
              _deletePage = function _deletePage(n) {
              if (n > 0 && n <= page) {
                  pages.splice(n, 1);
                  pagedim.splice(n, 1);
                  page--;
                  if (currentPage > page) {
                      currentPage = page;
                  }
                  this.setPage(currentPage);
              }
          },
              _setPage = function _setPage(n) {
              if (n > 0 && n <= page) {
                  currentPage = n;
                  pageWidth = pagedim[n].width;
                  pageHeight = pagedim[n].height;
              }
          },

          /**
           * Returns a document-specific font key - a label assigned to a
           * font name + font type combination at the time the font was added
           * to the font inventory.
           *
           * Font key is used as label for the desired font for a block of text
           * to be added to the PDF document stream.
           * @private
           * @function
           * @param fontName {String} can be undefined on "falthy" to indicate "use current"
           * @param fontStyle {String} can be undefined on "falthy" to indicate "use current"
           * @returns {String} Font key.
           */
          _getFont = function _getFont(fontName, fontStyle) {
              var key;

              fontName = fontName !== undefined ? fontName : fonts[activeFontKey].fontName;
              fontStyle = fontStyle !== undefined ? fontStyle : fonts[activeFontKey].fontStyle;

              if (fontName !== undefined) {
                  fontName = fontName.toLowerCase();
              }
              switch (fontName) {
                  case 'sans-serif':
                  case 'verdana':
                  case 'arial':
                  case 'helvetica':
                      fontName = 'helvetica';
                      break;
                  case 'fixed':
                  case 'monospace':
                  case 'terminal':
                  case 'courier':
                      fontName = 'courier';
                      break;
                  case 'serif':
                  case 'cursive':
                  case 'fantasy':
                  default:
                      fontName = 'times';
                      break;
              }

              try {
                  // get a string like 'F3' - the KEY corresponding tot he font + type combination.
                  key = fontmap[fontName][fontStyle];
              } catch (e) {}

              if (!key) {
                  //throw new Error("Unable to look up font label for font '" + fontName + "', '"
                  //+ fontStyle + "'. Refer to getFontList() for available fonts.");
                  key = fontmap['times'][fontStyle];
                  if (key == null) {
                      key = fontmap['times']['normal'];
                  }
              }
              return key;
          },
              buildDocument = function buildDocument() {
              outToPages = false; // switches out() to content

              objectNumber = 2;
              content_length = 0;
              content = [];
              offsets = [];
              additionalObjects = [];
              // Added for AcroForm
              events.publish('buildDocument');

              // putHeader()
              out('%PDF-' + pdfVersion);

              putPages();

              // Must happen after putPages
              // Modifies current object Id
              putAdditionalObjects();

              putResources();

              // Info
              newObject();
              out('<<');
              putInfo();
              out('>>');
              out('endobj');

              // Catalog
              newObject();
              out('<<');
              putCatalog();
              out('>>');
              out('endobj');

              // Cross-ref
              var o = content_length,
                  i,
                  p = "0000000000";
              out('xref');
              out('0 ' + (objectNumber + 1));
              out(p + ' 65535 f ');
              for (i = 1; i <= objectNumber; i++) {
                  var offset = offsets[i];
                  if (typeof offset === 'function') {
                      out((p + offsets[i]()).slice(-10) + ' 00000 n ');
                  } else {
                      out((p + offsets[i]).slice(-10) + ' 00000 n ');
                  }
              }
              // Trailer
              out('trailer');
              out('<<');
              putTrailer();
              out('>>');
              out('startxref');
              out('' + o);
              out('%%EOF');

              outToPages = true;

              return content.join('\n');
          },
              getStyle = function getStyle(style) {
              // see path-painting operators in PDF spec
              var op = 'S'; // stroke
              if (style === 'F') {
                  op = 'f'; // fill
              } else if (style === 'FD' || style === 'DF') {
                  op = 'B'; // both
              } else if (style === 'f' || style === 'f*' || style === 'B' || style === 'B*') {
                  /*
                   Allow direct use of these PDF path-painting operators:
                   - f	fill using nonzero winding number rule
                   - f*	fill using even-odd rule
                   - B	fill then stroke with fill using non-zero winding number rule
                   - B*	fill then stroke with fill using even-odd rule
                   */
                  op = style;
              }
              return op;
          },
              getArrayBuffer = function getArrayBuffer() {
              var data = buildDocument(),
                  len = data.length,
                  ab = new ArrayBuffer(len),
                  u8 = new Uint8Array(ab);

              while (len--) {
                  u8[len] = data.charCodeAt(len);
              }return ab;
          },
              getBlob = function getBlob() {
              return new Blob([getArrayBuffer()], { type: "application/pdf" });
          },

          /**
           * Generates the PDF document.
           *
           * If `type` argument is undefined, output is raw body of resulting PDF returned as a string.
           *
           * @param {String} type A string identifying one of the possible output types.
           * @param {Object} options An object providing some additional signalling to PDF generator.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name output
           */
          _output = SAFE(function (type, options) {
              var datauri = ('' + type).substr(0, 6) === 'dataur' ? 'data:application/pdf;base64,' + btoa(buildDocument()) : 0;

              switch (type) {
                  case undefined:
                      return buildDocument();
                  case 'save':
                      if (navigator.getUserMedia) {
                          if (global.URL === undefined || global.URL.createObjectURL === undefined) {
                              return API.output('dataurlnewwindow');
                          }
                      }
                      saveAs(getBlob(), options);
                      if (typeof saveAs.unload === 'function') {
                          if (global.setTimeout) {
                              setTimeout(saveAs.unload, 911);
                          }
                      }
                      break;
                  case 'arraybuffer':
                      return getArrayBuffer();
                  case 'blob':
                      return getBlob();
                  case 'bloburi':
                  case 'bloburl':
                      // User is responsible of calling revokeObjectURL
                      return global.URL && global.URL.createObjectURL(getBlob()) || void 0;
                  case 'datauristring':
                  case 'dataurlstring':
                      return datauri;
                  case 'dataurlnewwindow':
                      var nW = global.open(datauri);
                      if (nW || typeof safari === "undefined") return nW;
                  /* pass through */
                  case 'datauri':
                  case 'dataurl':
                      return global.document.location.href = datauri;
                  default:
                      throw new Error('Output type "' + type + '" is not supported.');
              }
              // @TODO: Add different output options
          });

          switch (unit) {
              case 'pt':
                  k = 1;
                  break;
              case 'mm':
                  k = 72 / 25.4000508;
                  break;
              case 'cm':
                  k = 72 / 2.54000508;
                  break;
              case 'in':
                  k = 72;
                  break;
              case 'px':
                  k = 96 / 72;
                  break;
              case 'pc':
                  k = 12;
                  break;
              case 'em':
                  k = 12;
                  break;
              case 'ex':
                  k = 6;
                  break;
              default:
                  throw 'Invalid unit: ' + unit;
          }

          //---------------------------------------
          // Public API

          /**
           * Object exposing internal API to plugins
           * @public
           */
          API.internal = {
              'pdfEscape': pdfEscape,
              'getStyle': getStyle,
              /**
               * Returns {FontObject} describing a particular font.
               * @public
               * @function
               * @param fontName {String} (Optional) Font's family name
               * @param fontStyle {String} (Optional) Font's style variation name (Example:"Italic")
               * @returns {FontObject}
               */
              'getFont': function getFont() {
                  return fonts[_getFont.apply(API, arguments)];
              },
              'getFontSize': function getFontSize() {
                  return activeFontSize;
              },
              'getLineHeight': function getLineHeight() {
                  return activeFontSize * lineHeightProportion;
              },
              'write': function write(string1 /*, string2, string3, etc */) {
                  out(arguments.length === 1 ? string1 : Array.prototype.join.call(arguments, ' '));
              },
              'getCoordinateString': function getCoordinateString(value) {
                  return f2(value * k);
              },
              'getVerticalCoordinateString': function getVerticalCoordinateString(value) {
                  return f2((pageHeight - value) * k);
              },
              'collections': {},
              'newObject': newObject,
              'newAdditionalObject': newAdditionalObject,
              'newObjectDeferred': newObjectDeferred,
              'newObjectDeferredBegin': newObjectDeferredBegin,
              'putStream': putStream,
              'events': events,
              // ratio that you use in multiplication of a given "size" number to arrive to 'point'
              // units of measurement.
              // scaleFactor is set at initialization of the document and calculated against the stated
              // default measurement units for the document.
              // If default is "mm", k is the number that will turn number in 'mm' into 'points' number.
              // through multiplication.
              'scaleFactor': k,
              'pageSize': {
                  get width() {
                      return pageWidth;
                  },
                  get height() {
                      return pageHeight;
                  }
              },
              'output': function output(type, options) {
                  return _output(type, options);
              },
              'getNumberOfPages': function getNumberOfPages() {
                  return pages.length - 1;
              },
              'pages': pages,
              'out': out,
              'f2': f2,
              'getPageInfo': function getPageInfo(pageNumberOneBased) {
                  var objId = (pageNumberOneBased - 1) * 2 + 3;
                  return { objId: objId, pageNumber: pageNumberOneBased, pageContext: pagesContext[pageNumberOneBased] };
              },
              'getCurrentPageInfo': function getCurrentPageInfo() {
                  var objId = (currentPage - 1) * 2 + 3;
                  return { objId: objId, pageNumber: currentPage, pageContext: pagesContext[currentPage] };
              },
              'getPDFVersion': function getPDFVersion() {
                  return pdfVersion;
              }
          };

          /**
           * Adds (and transfers the focus to) new page to the PDF document.
           * @function
           * @returns {jsPDF}
           *
           * @methodOf jsPDF#
           * @name addPage
           */
          API.addPage = function () {
              _addPage.apply(this, arguments);
              return this;
          };
          API.setPage = function () {
              _setPage.apply(this, arguments);
              return this;
          };
          API.insertPage = function (beforePage) {
              this.addPage();
              this.movePage(currentPage, beforePage);
              return this;
          };
          API.movePage = function (targetPage, beforePage) {
              if (targetPage > beforePage) {
                  var tmpPages = pages[targetPage];
                  var tmpPagedim = pagedim[targetPage];
                  var tmpPagesContext = pagesContext[targetPage];
                  for (var i = targetPage; i > beforePage; i--) {
                      pages[i] = pages[i - 1];
                      pagedim[i] = pagedim[i - 1];
                      pagesContext[i] = pagesContext[i - 1];
                  }
                  pages[beforePage] = tmpPages;
                  pagedim[beforePage] = tmpPagedim;
                  pagesContext[beforePage] = tmpPagesContext;
                  this.setPage(beforePage);
              } else if (targetPage < beforePage) {
                  var tmpPages = pages[targetPage];
                  var tmpPagedim = pagedim[targetPage];
                  var tmpPagesContext = pagesContext[targetPage];
                  for (var i = targetPage; i < beforePage; i++) {
                      pages[i] = pages[i + 1];
                      pagedim[i] = pagedim[i + 1];
                      pagesContext[i] = pagesContext[i + 1];
                  }
                  pages[beforePage] = tmpPages;
                  pagedim[beforePage] = tmpPagedim;
                  pagesContext[beforePage] = tmpPagesContext;
                  this.setPage(beforePage);
              }
              return this;
          };

          API.deletePage = function () {
              _deletePage.apply(this, arguments);
              return this;
          };
          API.setDisplayMode = function (zoom, layout, pmode) {
              zoomMode = zoom;
              layoutMode = layout;
              pageMode = pmode;
              return this;
          },

          /**
           * Adds text to page. Supports adding multiline text when 'text' argument is an Array of Strings.
           *
           * @function
           * @param {String|Array} text String or array of strings to be added to the page. Each line is shifted one line down per font, spacing settings declared before this call.
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Object} flags Collection of settings signalling how the text must be encoded. Defaults are sane. If you think you want to pass some flags, you likely can read the source.
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name text
           */
          API.text = function (text, x, y, flags, angle, align) {
              /**
               * Inserts something like this into PDF
               *   BT
               *    /F1 16 Tf  % Font name + size
               *    16 TL % How many units down for next line in multiline text
               *    0 g % color
               *    28.35 813.54 Td % position
               *    (line one) Tj
               *    T* (line two) Tj
               *    T* (line three) Tj
               *   ET
               */
              function ESC(s) {
                  s = s.split("\t").join(Array(options.TabLen || 9).join(" "));
                  return pdfEscape(s, flags);
              }

              // Pre-August-2012 the order of arguments was function(x, y, text, flags)
              // in effort to make all calls have similar signature like
              //   function(data, coordinates... , miscellaneous)
              // this method had its args flipped.
              // code below allows backward compatibility with old arg order.
              if (typeof text === 'number') {
                  tmp = y;
                  y = x;
                  x = text;
                  text = tmp;
              }

              // If there are any newlines in text, we assume
              // the user wanted to print multiple lines, so break the
              // text up into an array.  If the text is already an array,
              // we assume the user knows what they are doing.
              // Convert text into an array anyway to simplify
              // later code.
              if (typeof text === 'string') {
                  if (text.match(/[\n\r]/)) {
                      text = text.split(/\r\n|\r|\n/g);
                  } else {
                      text = [text];
                  }
              }
              if (typeof angle === 'string') {
                  align = angle;
                  angle = null;
              }
              if (typeof flags === 'string') {
                  align = flags;
                  flags = null;
              }
              if (typeof flags === 'number') {
                  angle = flags;
                  flags = null;
              }
              var xtra = '',
                  mode = 'Td',
                  todo;
              if (angle) {
                  angle *= Math.PI / 180;
                  var c = Math.cos(angle),
                      s = Math.sin(angle);
                  xtra = [f2(c), f2(s), f2(s * -1), f2(c), ''].join(" ");
                  mode = 'Tm';
              }
              flags = flags || {};
              if (!('noBOM' in flags)) flags.noBOM = true;
              if (!('autoencode' in flags)) flags.autoencode = true;

              var strokeOption = '';
              var pageContext = this.internal.getCurrentPageInfo().pageContext;
              if (true === flags.stroke) {
                  if (pageContext.lastTextWasStroke !== true) {
                      strokeOption = '1 Tr\n';
                      pageContext.lastTextWasStroke = true;
                  }
              } else {
                  if (pageContext.lastTextWasStroke) {
                      strokeOption = '0 Tr\n';
                  }
                  pageContext.lastTextWasStroke = false;
              }

              if (typeof this._runningPageHeight === 'undefined') {
                  this._runningPageHeight = 0;
              }

              if (typeof text === 'string') {
                  text = ESC(text);
              } else if (Object.prototype.toString.call(text) === '[object Array]') {
                  // we don't want to destroy  original text array, so cloning it
                  var sa = text.concat(),
                      da = [],
                      len = sa.length;
                  // we do array.join('text that must not be PDFescaped")
                  // thus, pdfEscape each component separately
                  while (len--) {
                      da.push(ESC(sa.shift()));
                  }
                  var linesLeft = Math.ceil((pageHeight - y - this._runningPageHeight) * k / (activeFontSize * lineHeightProportion));
                  if (0 <= linesLeft && linesLeft < da.length + 1) {
                      //todo = da.splice(linesLeft-1);
                  }

                  if (align) {
                      var left,
                          prevX,
                          maxLineLength,
                          leading = activeFontSize * lineHeightProportion,
                          lineWidths = text.map(function (v) {
                          return this.getStringUnitWidth(v) * activeFontSize / k;
                      }, this);
                      maxLineLength = Math.max.apply(Math, lineWidths);
                      // The first line uses the "main" Td setting,
                      // and the subsequent lines are offset by the
                      // previous line's x coordinate.
                      if (align === "center") {
                          // The passed in x coordinate defines
                          // the center point.
                          left = x - maxLineLength / 2;
                          x -= lineWidths[0] / 2;
                      } else if (align === "right") {
                          // The passed in x coordinate defines the
                          // rightmost point of the text.
                          left = x - maxLineLength;
                          x -= lineWidths[0];
                      } else {
                          throw new Error('Unrecognized alignment option, use "center" or "right".');
                      }
                      prevX = x;
                      text = da[0];
                      for (var i = 1, len = da.length; i < len; i++) {
                          var delta = maxLineLength - lineWidths[i];
                          if (align === "center") delta /= 2;
                          // T* = x-offset leading Td ( text )
                          text += ") Tj\n" + (left - prevX + delta) + " -" + leading + " Td (" + da[i];
                          prevX = left + delta;
                      }
                  } else {
                      text = da.join(") Tj\nT* (");
                  }
              } else {
                  throw new Error('Type of text must be string or Array. "' + text + '" is not recognized.');
              }
              // Using "'" ("go next line and render text" mark) would save space but would complicate our rendering code, templates

              // BT .. ET does NOT have default settings for Tf. You must state that explicitely every time for BT .. ET
              // if you want text transformation matrix (+ multiline) to work reliably (which reads sizes of things from font declarations)
              // Thus, there is NO useful, *reliable* concept of "default" font for a page.
              // The fact that "default" (reuse font used before) font worked before in basic cases is an accident
              // - readers dealing smartly with brokenness of jsPDF's markup.

              var curY;

              if (todo) {
                  //this.addPage();
                  //this._runningPageHeight += y -  (activeFontSize * 1.7 / k);
                  //curY = f2(pageHeight - activeFontSize * 1.7 /k);
              } else {
                  curY = f2((pageHeight - y) * k);
              }
              //curY = f2((pageHeight - (y - this._runningPageHeight)) * k);

              //			if (curY < 0){
              //				console.log('auto page break');
              //				this.addPage();
              //				this._runningPageHeight = y -  (activeFontSize * 1.7 / k);
              //				curY = f2(pageHeight - activeFontSize * 1.7 /k);
              //			}

              out('BT\n/' + activeFontKey + ' ' + activeFontSize + ' Tf\n' + // font face, style, size
              activeFontSize * lineHeightProportion + ' TL\n' + // line spacing
              strokeOption + // stroke option
              textColor + '\n' + xtra + f2(x * k) + ' ' + curY + ' ' + mode + '\n(' + text + ') Tj\nET');

              if (todo) {
                  //this.text( todo, x, activeFontSize * 1.7 / k);
                  //this.text( todo, x, this._runningPageHeight + (activeFontSize * 1.7 / k));
                  this.text(todo, x, y); // + (activeFontSize * 1.7 / k));
              }

              return this;
          };

          API.lstext = function (text, x, y, spacing) {
              for (var i = 0, len = text.length; i < len; i++, x += spacing) {
                  this.text(text[i], x, y);
              }
          };

          API.line = function (x1, y1, x2, y2) {
              return this.lines([[x2 - x1, y2 - y1]], x1, y1);
          };

          API.clip = function () {
              // By patrick-roberts, github.com/MrRio/jsPDF/issues/328
              // Call .clip() after calling .rect() with a style argument of null
              out('W'); // clip
              out('S'); // stroke path; necessary for clip to work
          };

          /**
           * This fixes the previous function clip(). Perhaps the 'stroke path' hack was due to the missing 'n' instruction?
           * We introduce the fixed version so as to not break API.
           * @param fillRule
           */
          API.clip_fixed = function (fillRule) {
              // Call .clip() after calling drawing ops with a style argument of null
              // W is the PDF clipping op
              if ('evenodd' === fillRule) {
                  out('W*');
              } else {
                  out('W');
              }
              // End the path object without filling or stroking it.
              // This operator is a path-painting no-op, used primarily for the side effect of changing the current clipping path
              // (see Section 4.4.3, “Clipping Path Operators”)
              out('n');
          };

          /**
           * Adds series of curves (straight lines or cubic bezier curves) to canvas, starting at `x`, `y` coordinates.
           * All data points in `lines` are relative to last line origin.
           * `x`, `y` become x1,y1 for first line / curve in the set.
           * For lines you only need to specify [x2, y2] - (ending point) vector against x1, y1 starting point.
           * For bezier curves you need to specify [x2,y2,x3,y3,x4,y4] - vectors to control points 1, 2, ending point. All vectors are against the start of the curve - x1,y1.
           *
           * @example .lines([[2,2],[-2,2],[1,1,2,2,3,3],[2,1]], 212,110, 10) // line, line, bezier curve, line
           * @param {Array} lines Array of *vector* shifts as pairs (lines) or sextets (cubic bezier curves).
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} scale (Defaults to [1.0,1.0]) x,y Scaling factor for all vectors. Elements can be any floating number Sub-one makes drawing smaller. Over-one grows the drawing. Negative flips the direction.
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @param {Boolean} closed If true, the path is closed with a straight line from the end of the last curve to the starting point.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name lines
           */
          API.lines = function (lines, x, y, scale, style, closed) {
              var scalex, scaley, i, l, leg, x2, y2, x3, y3, x4, y4;

              // Pre-August-2012 the order of arguments was function(x, y, lines, scale, style)
              // in effort to make all calls have similar signature like
              //   function(content, coordinateX, coordinateY , miscellaneous)
              // this method had its args flipped.
              // code below allows backward compatibility with old arg order.
              if (typeof lines === 'number') {
                  tmp = y;
                  y = x;
                  x = lines;
                  lines = tmp;
              }

              scale = scale || [1, 1];

              // starting point
              out(f3(x * k) + ' ' + f3((pageHeight - y) * k) + ' m ');

              scalex = scale[0];
              scaley = scale[1];
              l = lines.length;
              //, x2, y2 // bezier only. In page default measurement "units", *after* scaling
              //, x3, y3 // bezier only. In page default measurement "units", *after* scaling
              // ending point for all, lines and bezier. . In page default measurement "units", *after* scaling
              x4 = x; // last / ending point = starting point for first item.
              y4 = y; // last / ending point = starting point for first item.

              for (i = 0; i < l; i++) {
                  leg = lines[i];
                  if (leg.length === 2) {
                      // simple line
                      x4 = leg[0] * scalex + x4; // here last x4 was prior ending point
                      y4 = leg[1] * scaley + y4; // here last y4 was prior ending point
                      out(f3(x4 * k) + ' ' + f3((pageHeight - y4) * k) + ' l');
                  } else {
                      // bezier curve
                      x2 = leg[0] * scalex + x4; // here last x4 is prior ending point
                      y2 = leg[1] * scaley + y4; // here last y4 is prior ending point
                      x3 = leg[2] * scalex + x4; // here last x4 is prior ending point
                      y3 = leg[3] * scaley + y4; // here last y4 is prior ending point
                      x4 = leg[4] * scalex + x4; // here last x4 was prior ending point
                      y4 = leg[5] * scaley + y4; // here last y4 was prior ending point
                      out(f3(x2 * k) + ' ' + f3((pageHeight - y2) * k) + ' ' + f3(x3 * k) + ' ' + f3((pageHeight - y3) * k) + ' ' + f3(x4 * k) + ' ' + f3((pageHeight - y4) * k) + ' c');
                  }
              }

              if (closed) {
                  out(' h');
              }

              // stroking / filling / both the path
              if (style !== null) {
                  out(getStyle(style));
              }
              return this;
          };

          /**
           * Adds a rectangle to PDF
           *
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} w Width (in units declared at inception of PDF document)
           * @param {Number} h Height (in units declared at inception of PDF document)
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name rect
           */
          API.rect = function (x, y, w, h, style) {
              var op = getStyle(style);
              out([f2(x * k), f2((pageHeight - y) * k), f2(w * k), f2(-h * k), 're'].join(' '));

              if (style !== null) {
                  out(getStyle(style));
              }

              return this;
          };

          /**
           * Adds a triangle to PDF
           *
           * @param {Number} x1 Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y1 Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} x2 Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y2 Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} x3 Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y3 Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name triangle
           */
          API.triangle = function (x1, y1, x2, y2, x3, y3, style) {
              this.lines([[x2 - x1, y2 - y1], // vector to point 2
              [x3 - x2, y3 - y2], // vector to point 3
              [x1 - x3, y1 - y3] // closing vector back to point 1
              ], x1, y1, // start of path
              [1, 1], style, true);
              return this;
          };

          /**
           * Adds a rectangle with rounded corners to PDF
           *
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} w Width (in units declared at inception of PDF document)
           * @param {Number} h Height (in units declared at inception of PDF document)
           * @param {Number} rx Radius along x axis (in units declared at inception of PDF document)
           * @param {Number} rx Radius along y axis (in units declared at inception of PDF document)
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name roundedRect
           */
          API.roundedRect = function (x, y, w, h, rx, ry, style) {
              var MyArc = 4 / 3 * (Math.SQRT2 - 1);
              this.lines([[w - 2 * rx, 0], [rx * MyArc, 0, rx, ry - ry * MyArc, rx, ry], [0, h - 2 * ry], [0, ry * MyArc, -(rx * MyArc), ry, -rx, ry], [-w + 2 * rx, 0], [-(rx * MyArc), 0, -rx, -(ry * MyArc), -rx, -ry], [0, -h + 2 * ry], [0, -(ry * MyArc), rx * MyArc, -ry, rx, -ry]], x + rx, y, // start of path
              [1, 1], style);
              return this;
          };

          /**
           * Adds an ellipse to PDF
           *
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} rx Radius along x axis (in units declared at inception of PDF document)
           * @param {Number} rx Radius along y axis (in units declared at inception of PDF document)
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name ellipse
           */
          API.ellipse = function (x, y, rx, ry, style) {
              var lx = 4 / 3 * (Math.SQRT2 - 1) * rx,
                  ly = 4 / 3 * (Math.SQRT2 - 1) * ry;

              out([f2((x + rx) * k), f2((pageHeight - y) * k), 'm', f2((x + rx) * k), f2((pageHeight - (y - ly)) * k), f2((x + lx) * k), f2((pageHeight - (y - ry)) * k), f2(x * k), f2((pageHeight - (y - ry)) * k), 'c'].join(' '));
              out([f2((x - lx) * k), f2((pageHeight - (y - ry)) * k), f2((x - rx) * k), f2((pageHeight - (y - ly)) * k), f2((x - rx) * k), f2((pageHeight - y) * k), 'c'].join(' '));
              out([f2((x - rx) * k), f2((pageHeight - (y + ly)) * k), f2((x - lx) * k), f2((pageHeight - (y + ry)) * k), f2(x * k), f2((pageHeight - (y + ry)) * k), 'c'].join(' '));
              out([f2((x + lx) * k), f2((pageHeight - (y + ry)) * k), f2((x + rx) * k), f2((pageHeight - (y + ly)) * k), f2((x + rx) * k), f2((pageHeight - y) * k), 'c'].join(' '));

              if (style !== null) {
                  out(getStyle(style));
              }

              return this;
          };

          /**
           * Adds an circle to PDF
           *
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} r Radius (in units declared at inception of PDF document)
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name circle
           */
          API.circle = function (x, y, r, style) {
              return this.ellipse(x, y, r, r, style);
          };

          /**
           * Adds a properties to the PDF document
           *
           * @param {Object} A property_name-to-property_value object structure.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setProperties
           */
          API.setProperties = function (properties) {
              // copying only those properties we can render.
              for (var property in documentProperties) {
                  if (documentProperties.hasOwnProperty(property) && properties[property]) {
                      documentProperties[property] = properties[property];
                  }
              }
              return this;
          };

          /**
           * Sets font size for upcoming text elements.
           *
           * @param {Number} size Font size in points.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setFontSize
           */
          API.setFontSize = function (size) {
              activeFontSize = size;
              return this;
          };

          /**
           * Sets text font face, variant for upcoming text elements.
           * See output of jsPDF.getFontList() for possible font names, styles.
           *
           * @param {String} fontName Font name or family. Example: "times"
           * @param {String} fontStyle Font style or variant. Example: "italic"
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setFont
           */
          API.setFont = function (fontName, fontStyle) {
              activeFontKey = _getFont(fontName, fontStyle);
              // if font is not found, the above line blows up and we never go further
              return this;
          };

          /**
           * Switches font style or variant for upcoming text elements,
           * while keeping the font face or family same.
           * See output of jsPDF.getFontList() for possible font names, styles.
           *
           * @param {String} style Font style or variant. Example: "italic"
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setFontStyle
           */
          API.setFontStyle = API.setFontType = function (style) {
              activeFontKey = _getFont(undefined, style);
              // if font is not found, the above line blows up and we never go further
              return this;
          };

          /**
           * Returns an object - a tree of fontName to fontStyle relationships available to
           * active PDF document.
           *
           * @public
           * @function
           * @returns {Object} Like {'times':['normal', 'italic', ... ], 'arial':['normal', 'bold', ... ], ... }
           * @methodOf jsPDF#
           * @name getFontList
           */
          API.getFontList = function () {
              // TODO: iterate over fonts array or return copy of fontmap instead in case more are ever added.
              var list = {},
                  fontName,
                  fontStyle,
                  tmp;

              for (fontName in fontmap) {
                  if (fontmap.hasOwnProperty(fontName)) {
                      list[fontName] = tmp = [];
                      for (fontStyle in fontmap[fontName]) {
                          if (fontmap[fontName].hasOwnProperty(fontStyle)) {
                              tmp.push(fontStyle);
                          }
                      }
                  }
              }

              return list;
          };

          /**
           * Add a custom font.
           *
           * @param {String} Postscript name of the Font.  Example: "Menlo-Regular"
           * @param {String} Name of font-family from @font-face definition.  Example: "Menlo Regular"
           * @param {String} Font style.  Example: "normal"
           * @function
           * @returns the {fontKey} (same as the internal method)
           * @methodOf jsPDF#
           * @name addFont
           */
          API.addFont = function (postScriptName, fontName, fontStyle) {
              addFont(postScriptName, fontName, fontStyle, 'StandardEncoding');
          };

          /**
           * Sets line width for upcoming lines.
           *
           * @param {Number} width Line width (in units declared at inception of PDF document)
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setLineWidth
           */
          API.setLineWidth = function (width) {
              out((width * k).toFixed(2) + ' w');
              return this;
          };

          /**
           * Sets the stroke color for upcoming elements.
           *
           * Depending on the number of arguments given, Gray, RGB, or CMYK
           * color space is implied.
           *
           * When only ch1 is given, "Gray" color space is implied and it
           * must be a value in the range from 0.00 (solid black) to to 1.00 (white)
           * if values are communicated as String types, or in range from 0 (black)
           * to 255 (white) if communicated as Number type.
           * The RGB-like 0-255 range is provided for backward compatibility.
           *
           * When only ch1,ch2,ch3 are given, "RGB" color space is implied and each
           * value must be in the range from 0.00 (minimum intensity) to to 1.00
           * (max intensity) if values are communicated as String types, or
           * from 0 (min intensity) to to 255 (max intensity) if values are communicated
           * as Number types.
           * The RGB-like 0-255 range is provided for backward compatibility.
           *
           * When ch1,ch2,ch3,ch4 are given, "CMYK" color space is implied and each
           * value must be a in the range from 0.00 (0% concentration) to to
           * 1.00 (100% concentration)
           *
           * Because JavaScript treats fixed point numbers badly (rounds to
           * floating point nearest to binary representation) it is highly advised to
           * communicate the fractional numbers as String types, not JavaScript Number type.
           *
           * @param {Number|String} ch1 Color channel value
           * @param {Number|String} ch2 Color channel value
           * @param {Number|String} ch3 Color channel value
           * @param {Number|String} ch4 Color channel value
           *
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setDrawColor
           */
          API.setDrawColor = function (ch1, ch2, ch3, ch4) {
              var color;
              if (ch2 === undefined || ch4 === undefined && ch1 === ch2 === ch3) {
                  // Gray color space.
                  if (typeof ch1 === 'string') {
                      color = ch1 + ' G';
                  } else {
                      color = f2(ch1 / 255) + ' G';
                  }
              } else if (ch4 === undefined) {
                  // RGB
                  if (typeof ch1 === 'string') {
                      color = [ch1, ch2, ch3, 'RG'].join(' ');
                  } else {
                      color = [f2(ch1 / 255), f2(ch2 / 255), f2(ch3 / 255), 'RG'].join(' ');
                  }
              } else {
                  // CMYK
                  if (typeof ch1 === 'string') {
                      color = [ch1, ch2, ch3, ch4, 'K'].join(' ');
                  } else {
                      color = [f2(ch1), f2(ch2), f2(ch3), f2(ch4), 'K'].join(' ');
                  }
              }

              out(color);
              return this;
          };

          /**
           * Sets the fill color for upcoming elements.
           *
           * Depending on the number of arguments given, Gray, RGB, or CMYK
           * color space is implied.
           *
           * When only ch1 is given, "Gray" color space is implied and it
           * must be a value in the range from 0.00 (solid black) to to 1.00 (white)
           * if values are communicated as String types, or in range from 0 (black)
           * to 255 (white) if communicated as Number type.
           * The RGB-like 0-255 range is provided for backward compatibility.
           *
           * When only ch1,ch2,ch3 are given, "RGB" color space is implied and each
           * value must be in the range from 0.00 (minimum intensity) to to 1.00
           * (max intensity) if values are communicated as String types, or
           * from 0 (min intensity) to to 255 (max intensity) if values are communicated
           * as Number types.
           * The RGB-like 0-255 range is provided for backward compatibility.
           *
           * When ch1,ch2,ch3,ch4 are given, "CMYK" color space is implied and each
           * value must be a in the range from 0.00 (0% concentration) to to
           * 1.00 (100% concentration)
           *
           * Because JavaScript treats fixed point numbers badly (rounds to
           * floating point nearest to binary representation) it is highly advised to
           * communicate the fractional numbers as String types, not JavaScript Number type.
           *
           * @param {Number|String} ch1 Color channel value
           * @param {Number|String} ch2 Color channel value
           * @param {Number|String} ch3 Color channel value
           * @param {Number|String} ch4 Color channel value
           *
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setFillColor
           */
          API.setFillColor = function (ch1, ch2, ch3, ch4) {
              var color;

              if (ch2 === undefined || ch4 === undefined && ch1 === ch2 === ch3) {
                  // Gray color space.
                  if (typeof ch1 === 'string') {
                      color = ch1 + ' g';
                  } else {
                      color = f2(ch1 / 255) + ' g';
                  }
              } else if (ch4 === undefined || (typeof ch4 === 'undefined' ? 'undefined' : _typeof(ch4)) === 'object') {
                  // RGB
                  if (typeof ch1 === 'string') {
                      color = [ch1, ch2, ch3, 'rg'].join(' ');
                  } else {
                      color = [f2(ch1 / 255), f2(ch2 / 255), f2(ch3 / 255), 'rg'].join(' ');
                  }
                  if (ch4 && ch4.a === 0) {
                      //TODO Implement transparency.
                      //WORKAROUND use white for now
                      color = ['255', '255', '255', 'rg'].join(' ');
                  }
              } else {
                  // CMYK
                  if (typeof ch1 === 'string') {
                      color = [ch1, ch2, ch3, ch4, 'k'].join(' ');
                  } else {
                      color = [f2(ch1), f2(ch2), f2(ch3), f2(ch4), 'k'].join(' ');
                  }
              }

              out(color);
              return this;
          };

          /**
           * Sets the text color for upcoming elements.
           * If only one, first argument is given,
           * treats the value as gray-scale color value.
           *
           * @param {Number} r Red channel color value in range 0-255 or {String} r color value in hexadecimal, example: '#FFFFFF'
           * @param {Number} g Green channel color value in range 0-255
           * @param {Number} b Blue channel color value in range 0-255
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setTextColor
           */
          API.setTextColor = function (r, g, b) {
              if (typeof r === 'string' && /^#[0-9A-Fa-f]{6}$/.test(r)) {
                  var hex = parseInt(r.substr(1), 16);
                  r = hex >> 16 & 255;
                  g = hex >> 8 & 255;
                  b = hex & 255;
              }

              if (r === 0 && g === 0 && b === 0 || typeof g === 'undefined') {
                  textColor = f3(r / 255) + ' g';
              } else {
                  textColor = [f3(r / 255), f3(g / 255), f3(b / 255), 'rg'].join(' ');
              }
              return this;
          };

          /**
           * Is an Object providing a mapping from human-readable to
           * integer flag values designating the varieties of line cap
           * and join styles.
           *
           * @returns {Object}
           * @fieldOf jsPDF#
           * @name CapJoinStyles
           */
          API.CapJoinStyles = {
              0: 0,
              'butt': 0,
              'but': 0,
              'miter': 0,
              1: 1,
              'round': 1,
              'rounded': 1,
              'circle': 1,
              2: 2,
              'projecting': 2,
              'project': 2,
              'square': 2,
              'bevel': 2
          };

          /**
           * Sets the line cap styles
           * See {jsPDF.CapJoinStyles} for variants
           *
           * @param {String|Number} style A string or number identifying the type of line cap
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setLineCap
           */
          API.setLineCap = function (style) {
              var id = this.CapJoinStyles[style];
              if (id === undefined) {
                  throw new Error("Line cap style of '" + style + "' is not recognized. See or extend .CapJoinStyles property for valid styles");
              }
              lineCapID = id;
              out(id + ' J');

              return this;
          };

          /**
           * Sets the line join styles
           * See {jsPDF.CapJoinStyles} for variants
           *
           * @param {String|Number} style A string or number identifying the type of line join
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setLineJoin
           */
          API.setLineJoin = function (style) {
              var id = this.CapJoinStyles[style];
              if (id === undefined) {
                  throw new Error("Line join style of '" + style + "' is not recognized. See or extend .CapJoinStyles property for valid styles");
              }
              lineJoinID = id;
              out(id + ' j');

              return this;
          };

          // Output is both an internal (for plugins) and external function
          API.output = _output;

          /**
           * Saves as PDF document. An alias of jsPDF.output('save', 'filename.pdf')
           * @param  {String} filename The filename including extension.
           *
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name save
           */
          API.save = function (filename) {
              API.output('save', filename);
          };

          // applying plugins (more methods) ON TOP of built-in API.
          // this is intentional as we allow plugins to override
          // built-ins
          for (var plugin in jsPDF.API) {
              if (jsPDF.API.hasOwnProperty(plugin)) {
                  if (plugin === 'events' && jsPDF.API.events.length) {
                      (function (events, newEvents) {

                          // jsPDF.API.events is a JS Array of Arrays
                          // where each Array is a pair of event name, handler
                          // Events were added by plugins to the jsPDF instantiator.
                          // These are always added to the new instance and some ran
                          // during instantiation.
                          var eventname, handler_and_args, i;

                          for (i = newEvents.length - 1; i !== -1; i--) {
                              // subscribe takes 3 args: 'topic', function, runonce_flag
                              // if undefined, runonce is false.
                              // users can attach callback directly,
                              // or they can attach an array with [callback, runonce_flag]
                              // that's what the "apply" magic is for below.
                              eventname = newEvents[i][0];
                              handler_and_args = newEvents[i][1];
                              events.subscribe.apply(events, [eventname].concat(typeof handler_and_args === 'function' ? [handler_and_args] : handler_and_args));
                          }
                      })(events, jsPDF.API.events);
                  } else {
                      API[plugin] = jsPDF.API[plugin];
                  }
              }
          }

          //////////////////////////////////////////////////////
          // continuing initialization of jsPDF Document object
          //////////////////////////////////////////////////////
          // Add the first page automatically
          addFonts();
          activeFontKey = 'F1';
          _addPage(format, orientation);

          events.publish('initialized');
          return API;
      }

      /**
       * jsPDF.API is a STATIC property of jsPDF class.
       * jsPDF.API is an object you can add methods and properties to.
       * The methods / properties you add will show up in new jsPDF objects.
       *
       * One property is prepopulated. It is the 'events' Object. Plugin authors can add topics,
       * callbacks to this object. These will be reassigned to all new instances of jsPDF.
       * Examples:
       * jsPDF.API.events['initialized'] = function(){ 'this' is API object }
       * jsPDF.API.events['addFont'] = function(added_font_object){ 'this' is API object }
       *
       * @static
       * @public
       * @memberOf jsPDF
       * @name API
       *
       * @example
       * jsPDF.API.mymethod = function(){
      *   // 'this' will be ref to internal API object. see jsPDF source
      *   // , so you can refer to built-in methods like so:
      *   //     this.line(....)
      *   //     this.text(....)
      * }
       * var pdfdoc = new jsPDF()
       * pdfdoc.mymethod() // <- !!!!!!
       */
      jsPDF.API = { events: [] };
      jsPDF.version = "1.3.0 2016-09-28T10:52:40.410Z:jameshall";

      if (typeof define === 'function' && define.amd) {
          define('jsPDF', function () {
              return jsPDF;
          });
      } else if (typeof module !== 'undefined' && module.exports) {
          module.exports = jsPDF;
      } else {
          global.jsPDF = jsPDF;
      }
      return jsPDF;
  }(typeof self !== "undefined" && self || typeof window !== "undefined" && window || undefined);
  /*rollup-keeper-start*/window.tmp = jsPDF;

  /**
   * jsPDF AcroForm Plugin
   * Copyright (c) 2016 Alexander Weidt, https://github.com/BiggA94
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  (window.AcroForm = function (jsPDFAPI) {
      'use strict';

      var AcroForm = window.AcroForm;

      AcroForm.scale = function (x) {
          return x * (acroformPlugin.internal.scaleFactor / 1); // 1 = (96 / 72)
      };
      AcroForm.antiScale = function (x) {
          return 1 / acroformPlugin.internal.scaleFactor * x;
      };

      var acroformPlugin = {
          fields: [],
          xForms: [],
          /**
           * acroFormDictionaryRoot contains information about the AcroForm Dictionary
           * 0: The Event-Token, the AcroFormDictionaryCallback has
           * 1: The Object ID of the Root
           */
          acroFormDictionaryRoot: null,
          /**
           * After the PDF gets evaluated, the reference to the root has to be reset,
           * this indicates, whether the root has already been printed out
           */
          printedOut: false,
          internal: null
      };

      jsPDF.API.acroformPlugin = acroformPlugin;

      var annotReferenceCallback = function annotReferenceCallback() {
          for (var i in this.acroformPlugin.acroFormDictionaryRoot.Fields) {
              var formObject = this.acroformPlugin.acroFormDictionaryRoot.Fields[i];
              // add Annot Reference!
              if (formObject.hasAnnotation) {
                  // If theres an Annotation Widget in the Form Object, put the Reference in the /Annot array
                  createAnnotationReference.call(this, formObject);
              }
          }
      };

      var createAcroForm = function createAcroForm() {
          if (this.acroformPlugin.acroFormDictionaryRoot) {
              //return;
              throw new Error("Exception while creating AcroformDictionary");
          }

          // The Object Number of the AcroForm Dictionary
          this.acroformPlugin.acroFormDictionaryRoot = new AcroForm.AcroFormDictionary();

          this.acroformPlugin.internal = this.internal;

          // add Callback for creating the AcroForm Dictionary
          this.acroformPlugin.acroFormDictionaryRoot._eventID = this.internal.events.subscribe('postPutResources', AcroFormDictionaryCallback);

          this.internal.events.subscribe('buildDocument', annotReferenceCallback); //buildDocument

          // Register event, that is triggered when the DocumentCatalog is written, in order to add /AcroForm
          this.internal.events.subscribe('putCatalog', putCatalogCallback);

          // Register event, that creates all Fields
          this.internal.events.subscribe('postPutPages', createFieldCallback);
      };

      /**
       * Create the Reference to the widgetAnnotation, so that it gets referenced in the Annot[] int the+
       * (Requires the Annotation Plugin)
       */
      var createAnnotationReference = function createAnnotationReference(object) {
          var options = {
              type: 'reference',
              object: object
          };
          jsPDF.API.annotationPlugin.annotations[this.internal.getPageInfo(object.page).pageNumber].push(options);
      };

      var putForm = function putForm(formObject) {
          if (this.acroformPlugin.printedOut) {
              this.acroformPlugin.printedOut = false;
              this.acroformPlugin.acroFormDictionaryRoot = null;
          }
          if (!this.acroformPlugin.acroFormDictionaryRoot) {
              createAcroForm.call(this);
          }
          this.acroformPlugin.acroFormDictionaryRoot.Fields.push(formObject);
      };

      // Callbacks

      var putCatalogCallback = function putCatalogCallback() {
          //Put reference to AcroForm to DocumentCatalog
          if (typeof this.acroformPlugin.acroFormDictionaryRoot != 'undefined') {
              // for safety, shouldn't normally be the case
              this.internal.write('/AcroForm ' + this.acroformPlugin.acroFormDictionaryRoot.objId + ' ' + 0 + ' R');
          } else {
              console.log('Root missing...');
          }
      };

      /**
       * Adds /Acroform X 0 R to Document Catalog,
       * and creates the AcroForm Dictionary
       */
      var AcroFormDictionaryCallback = function AcroFormDictionaryCallback() {
          // Remove event
          this.internal.events.unsubscribe(this.acroformPlugin.acroFormDictionaryRoot._eventID);

          delete this.acroformPlugin.acroFormDictionaryRoot._eventID;

          this.acroformPlugin.printedOut = true;
      };

      /**
       * Creates the single Fields and writes them into the Document
       *
       * If fieldArray is set, use the fields that are inside it instead of the fields from the AcroRoot
       * (for the FormXObjects...)
       */
      var createFieldCallback = function createFieldCallback(fieldArray) {
          var standardFields = !fieldArray;

          if (!fieldArray) {
              // in case there is no fieldArray specified, we want to print out the Fields of the AcroForm
              // Print out Root
              this.internal.newObjectDeferredBegin(this.acroformPlugin.acroFormDictionaryRoot.objId);
              this.internal.out(this.acroformPlugin.acroFormDictionaryRoot.getString());
          }

          var fieldArray = fieldArray || this.acroformPlugin.acroFormDictionaryRoot.Kids;

          for (var i in fieldArray) {
              var key = i;
              var form = fieldArray[i];

              var oldRect = form.Rect;

              if (form.Rect) {
                  form.Rect = AcroForm.internal.calculateCoordinates.call(this, form.Rect);
              }

              // Start Writing the Object
              this.internal.newObjectDeferredBegin(form.objId);

              var content = "";
              content += form.objId + " 0 obj\n";

              content += "<<\n" + form.getContent();

              form.Rect = oldRect;

              if (form.hasAppearanceStream && !form.appearanceStreamContent) {
                  // Calculate Appearance
                  var appearance = AcroForm.internal.calculateAppearanceStream.call(this, form);
                  content += "/AP << /N " + appearance + " >>\n";

                  this.acroformPlugin.xForms.push(appearance);
              }

              // Assume AppearanceStreamContent is a Array with N,R,D (at least one of them!)
              if (form.appearanceStreamContent) {
                  content += "/AP << ";
                  // Iterate over N,R and D
                  for (var k in form.appearanceStreamContent) {
                      var value = form.appearanceStreamContent[k];
                      content += "/" + k + " ";
                      content += "<< ";
                      if (Object.keys(value).length >= 1 || Array.isArray(value)) {
                          // appearanceStream is an Array or Object!
                          for (var i in value) {
                              var obj = value[i];
                              if (typeof obj === 'function') {
                                  // if Function is referenced, call it in order to get the FormXObject
                                  obj = obj.call(this, form);
                              }
                              content += "/" + i + " " + obj + " ";

                              // In case the XForm is already used, e.g. OffState of CheckBoxes, don't add it
                              if (!(this.acroformPlugin.xForms.indexOf(obj) >= 0)) this.acroformPlugin.xForms.push(obj);
                          }
                      } else {
                          var obj = value;
                          if (typeof obj === 'function') {
                              // if Function is referenced, call it in order to get the FormXObject
                              obj = obj.call(this, form);
                          }
                          content += "/" + i + " " + obj + " \n";
                          if (!(this.acroformPlugin.xForms.indexOf(obj) >= 0)) this.acroformPlugin.xForms.push(obj);
                      }
                      content += " >>\n";
                  }

                  // appearance stream is a normal Object..
                  content += ">>\n";
              }

              content += ">>\nendobj\n";

              this.internal.out(content);
          }
          if (standardFields) {
              createXFormObjectCallback.call(this, this.acroformPlugin.xForms);
          }
      };

      var createXFormObjectCallback = function createXFormObjectCallback(fieldArray) {
          for (var i in fieldArray) {
              var key = i;
              var form = fieldArray[i];
              // Start Writing the Object
              this.internal.newObjectDeferredBegin(form && form.objId);

              var content = "";
              content += form ? form.getString() : '';
              this.internal.out(content);

              delete fieldArray[key];
          }
      };

      // Public:

      jsPDFAPI.addField = function (fieldObject) {
          //var opt = parseOptions(fieldObject);
          if (fieldObject instanceof AcroForm.TextField) {
              addTextField.call(this, fieldObject);
          } else if (fieldObject instanceof AcroForm.ChoiceField) {
              addChoiceField.call(this, fieldObject);
          } else if (fieldObject instanceof AcroForm.Button) {
              addButton.call(this, fieldObject);
          } else if (fieldObject instanceof AcroForm.ChildClass) {
              putForm.call(this, fieldObject);
          } else if (fieldObject) {
              // try to put..
              putForm.call(this, fieldObject);
          }
          fieldObject.page = this.acroformPlugin.internal.getCurrentPageInfo().pageNumber;
          return this;
      };

      // ############### sort in:

      /**
       * Button
       * FT = Btn
       */
      var addButton = function addButton(options) {
          var options = options || new AcroForm.Field();

          options.FT = '/Btn';

          /**
           * Calculating the Ff entry:
           *
           * The Ff entry contains flags, that have to be set bitwise
           * In the Following the number in the Comment is the BitPosition
           */
          var flags = options.Ff || 0;

          // 17, Pushbutton
          if (options.pushbutton) {
              // Options.pushbutton should be 1 or 0
              flags = AcroForm.internal.setBitPosition(flags, 17);
              delete options.pushbutton;
          }

          //16, Radio
          if (options.radio) {
              //flags = options.Ff | options.radio << 15;
              flags = AcroForm.internal.setBitPosition(flags, 16);
              delete options.radio;
          }

          // 15, NoToggleToOff (Radio buttons only
          if (options.noToggleToOff) {
              //flags = options.Ff | options.noToggleToOff << 14;
              flags = AcroForm.internal.setBitPosition(flags, 15);
              //delete options.noToggleToOff;
          }

          // In case, there is no Flag set, it is a check-box
          options.Ff = flags;

          putForm.call(this, options);
      };

      var addTextField = function addTextField(options) {
          var options = options || new AcroForm.Field();

          options.FT = '/Tx';

          /**
           * Calculating the Ff entry:
           *
           * The Ff entry contains flags, that have to be set bitwise
           * In the Following the number in the Comment is the BitPosition
           */

          var flags = options.Ff || 0;

          // 13, multiline
          if (options.multiline) {
              // Set Flag
              flags = flags | 1 << 12;
              // Remove multiline from FieldObject
              //delete options.multiline;
          }

          // 14, Password
          if (options.password) {
              flags = flags | 1 << 13;
              //delete options.password;
          }

          // 21, FileSelect, PDF 1.4...
          if (options.fileSelect) {
              flags = flags | 1 << 20;
              //delete options.fileSelect;
          }

          // 23, DoNotSpellCheck, PDF 1.4...
          if (options.doNotSpellCheck) {
              flags = flags | 1 << 22;
              //delete options.doNotSpellCheck;
          }

          // 24, DoNotScroll, PDF 1.4...
          if (options.doNotScroll) {
              flags = flags | 1 << 23;
              //delete options.doNotScroll;
          }

          options.Ff = options.Ff || flags;

          // Add field
          putForm.call(this, options);
      };

      var addChoiceField = function addChoiceField(opt) {
          var options = opt || new AcroForm.Field();

          options.FT = '/Ch';

          /**
           * Calculating the Ff entry:
           *
           * The Ff entry contains flags, that have to be set bitwise
           * In the Following the number in the Comment is the BitPosition
           */

          var flags = options.Ff || 0;

          // 18, Combo (If not set, the choiceField is a listBox!!)
          if (options.combo) {
              // Set Flag
              flags = AcroForm.internal.setBitPosition(flags, 18);
              // Remove combo from FieldObject
              delete options.combo;
          }

          // 19, Edit
          if (options.edit) {
              flags = AcroForm.internal.setBitPosition(flags, 19);
              delete options.edit;
          }

          // 20, Sort
          if (options.sort) {
              flags = AcroForm.internal.setBitPosition(flags, 20);
              delete options.sort;
          }

          // 22, MultiSelect (PDF 1.4)
          if (options.multiSelect && this.internal.getPDFVersion() >= 1.4) {
              flags = AcroForm.internal.setBitPosition(flags, 22);
              delete options.multiSelect;
          }

          // 23, DoNotSpellCheck (PDF 1.4)
          if (options.doNotSpellCheck && this.internal.getPDFVersion() >= 1.4) {
              flags = AcroForm.internal.setBitPosition(flags, 23);
              delete options.doNotSpellCheck;
          }

          options.Ff = flags;

          //options.hasAnnotation = true;

          // Add field
          putForm.call(this, options);
      };
  })(jsPDF.API);

  var AcroForm = window.AcroForm;

  AcroForm.internal = {};

  AcroForm.createFormXObject = function (formObject) {
      var xobj = new AcroForm.FormXObject();
      var height = AcroForm.Appearance.internal.getHeight(formObject) || 0;
      var width = AcroForm.Appearance.internal.getWidth(formObject) || 0;
      xobj.BBox = [0, 0, width, height];
      return xobj;
  };

  // Contains Methods for creating standard appearances
  AcroForm.Appearance = {
      CheckBox: {
          createAppearanceStream: function createAppearanceStream() {
              var appearance = {
                  N: {
                      On: AcroForm.Appearance.CheckBox.YesNormal
                  },
                  D: {
                      On: AcroForm.Appearance.CheckBox.YesPushDown,
                      Off: AcroForm.Appearance.CheckBox.OffPushDown
                  }
              };

              return appearance;
          },
          /**
           * If any other icons are needed, the number between the brackets can be changed
           * @returns {string}
           */
          createMK: function createMK() {
              // 3-> Hook
              return "<< /CA (3)>>";
          },
          /**
           * Returns the standard On Appearance for a CheckBox
           * @returns {AcroForm.FormXObject}
           */
          YesPushDown: function YesPushDown(formObject) {
              var xobj = AcroForm.createFormXObject(formObject);
              var stream = "";
              // F13 is ZapfDingbats (Symbolic)
              formObject.Q = 1; // set text-alignment as centered
              var calcRes = AcroForm.internal.calculateX(formObject, "3", "ZapfDingbats", 50);
              stream += "0.749023 g\n\
             0 0 " + AcroForm.Appearance.internal.getWidth(formObject) + " " + AcroForm.Appearance.internal.getHeight(formObject) + " re\n\
             f\n\
             BMC\n\
             q\n\
             0 0 1 rg\n\
             /F13 " + calcRes.fontSize + " Tf 0 g\n\
             BT\n";
              stream += calcRes.text;
              stream += "ET\n\
             Q\n\
             EMC\n";
              xobj.stream = stream;
              return xobj;
          },

          YesNormal: function YesNormal(formObject) {
              var xobj = AcroForm.createFormXObject(formObject);
              var stream = "";
              formObject.Q = 1; // set text-alignment as centered
              var calcRes = AcroForm.internal.calculateX(formObject, "3", "ZapfDingbats", AcroForm.Appearance.internal.getHeight(formObject) * 0.9);
              stream += "1 g\n\
0 0 " + AcroForm.Appearance.internal.getWidth(formObject) + " " + AcroForm.Appearance.internal.getHeight(formObject) + " re\n\
f\n\
q\n\
0 0 1 rg\n\
0 0 " + (AcroForm.Appearance.internal.getWidth(formObject) - 1) + " " + (AcroForm.Appearance.internal.getHeight(formObject) - 1) + " re\n\
W\n\
n\n\
0 g\n\
BT\n\
/F13 " + calcRes.fontSize + " Tf 0 g\n";
              stream += calcRes.text;
              stream += "ET\n\
             Q\n";
              xobj.stream = stream;
              return xobj;
          },

          /**
           * Returns the standard Off Appearance for a CheckBox
           * @returns {AcroForm.FormXObject}
           */
          OffPushDown: function OffPushDown(formObject) {
              var xobj = AcroForm.createFormXObject(formObject);
              var stream = "";
              stream += "0.749023 g\n\
            0 0 " + AcroForm.Appearance.internal.getWidth(formObject) + " " + AcroForm.Appearance.internal.getHeight(formObject) + " re\n\
            f\n";
              xobj.stream = stream;
              return xobj;
          }
      },

      RadioButton: {
          Circle: {
              createAppearanceStream: function createAppearanceStream(name) {
                  var appearanceStreamContent = {
                      D: {
                          'Off': AcroForm.Appearance.RadioButton.Circle.OffPushDown
                      },
                      N: {}
                  };
                  appearanceStreamContent.N[name] = AcroForm.Appearance.RadioButton.Circle.YesNormal;
                  appearanceStreamContent.D[name] = AcroForm.Appearance.RadioButton.Circle.YesPushDown;
                  return appearanceStreamContent;
              },
              createMK: function createMK() {
                  return "<< /CA (l)>>";
              },

              YesNormal: function YesNormal(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var stream = "";
                  // Make the Radius of the Circle relative to min(height, width) of formObject
                  var DotRadius = AcroForm.Appearance.internal.getWidth(formObject) <= AcroForm.Appearance.internal.getHeight(formObject) ? AcroForm.Appearance.internal.getWidth(formObject) / 4 : AcroForm.Appearance.internal.getHeight(formObject) / 4;
                  // The Borderpadding...
                  DotRadius *= 0.9;
                  var c = AcroForm.Appearance.internal.Bezier_C;
                  /*
                   The Following is a Circle created with Bezier-Curves.
                   */
                  stream += "q\n\
1 0 0 1 " + AcroForm.Appearance.internal.getWidth(formObject) / 2 + " " + AcroForm.Appearance.internal.getHeight(formObject) / 2 + " cm\n\
" + DotRadius + " 0 m\n\
" + DotRadius + " " + DotRadius * c + " " + DotRadius * c + " " + DotRadius + " 0 " + DotRadius + " c\n\
-" + DotRadius * c + " " + DotRadius + " -" + DotRadius + " " + DotRadius * c + " -" + DotRadius + " 0 c\n\
-" + DotRadius + " -" + DotRadius * c + " -" + DotRadius * c + " -" + DotRadius + " 0 -" + DotRadius + " c\n\
" + DotRadius * c + " -" + DotRadius + " " + DotRadius + " -" + DotRadius * c + " " + DotRadius + " 0 c\n\
f\n\
Q\n";
                  xobj.stream = stream;
                  return xobj;
              },
              YesPushDown: function YesPushDown(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var stream = "";
                  var DotRadius = AcroForm.Appearance.internal.getWidth(formObject) <= AcroForm.Appearance.internal.getHeight(formObject) ? AcroForm.Appearance.internal.getWidth(formObject) / 4 : AcroForm.Appearance.internal.getHeight(formObject) / 4;
                  // The Borderpadding...
                  DotRadius *= 0.9;
                  var c = AcroForm.Appearance.internal.Bezier_C;
                  stream += "0.749023 g\n\
            q\n\
           1 0 0 1 " + AcroForm.Appearance.internal.getWidth(formObject) / 2 + " " + AcroForm.Appearance.internal.getHeight(formObject) / 2 + " cm\n\
" + DotRadius * 2 + " 0 m\n\
" + DotRadius * 2 + " " + DotRadius * 2 * c + " " + DotRadius * 2 * c + " " + DotRadius * 2 + " 0 " + DotRadius * 2 + " c\n\
-" + DotRadius * 2 * c + " " + DotRadius * 2 + " -" + DotRadius * 2 + " " + DotRadius * 2 * c + " -" + DotRadius * 2 + " 0 c\n\
-" + DotRadius * 2 + " -" + DotRadius * 2 * c + " -" + DotRadius * 2 * c + " -" + DotRadius * 2 + " 0 -" + DotRadius * 2 + " c\n\
" + DotRadius * 2 * c + " -" + DotRadius * 2 + " " + DotRadius * 2 + " -" + DotRadius * 2 * c + " " + DotRadius * 2 + " 0 c\n\
            f\n\
            Q\n\
            0 g\n\
            q\n\
            1 0 0 1 " + AcroForm.Appearance.internal.getWidth(formObject) / 2 + " " + AcroForm.Appearance.internal.getHeight(formObject) / 2 + " cm\n\
" + DotRadius + " 0 m\n\
" + DotRadius + " " + DotRadius * c + " " + DotRadius * c + " " + DotRadius + " 0 " + DotRadius + " c\n\
-" + DotRadius * c + " " + DotRadius + " -" + DotRadius + " " + DotRadius * c + " -" + DotRadius + " 0 c\n\
-" + DotRadius + " -" + DotRadius * c + " -" + DotRadius * c + " -" + DotRadius + " 0 -" + DotRadius + " c\n\
" + DotRadius * c + " -" + DotRadius + " " + DotRadius + " -" + DotRadius * c + " " + DotRadius + " 0 c\n\
            f\n\
            Q\n";
                  xobj.stream = stream;
                  return xobj;
              },
              OffPushDown: function OffPushDown(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var stream = "";
                  var DotRadius = AcroForm.Appearance.internal.getWidth(formObject) <= AcroForm.Appearance.internal.getHeight(formObject) ? AcroForm.Appearance.internal.getWidth(formObject) / 4 : AcroForm.Appearance.internal.getHeight(formObject) / 4;
                  // The Borderpadding...
                  DotRadius *= 0.9;
                  var c = AcroForm.Appearance.internal.Bezier_C;
                  stream += "0.749023 g\n\
            q\n\
 1 0 0 1 " + AcroForm.Appearance.internal.getWidth(formObject) / 2 + " " + AcroForm.Appearance.internal.getHeight(formObject) / 2 + " cm\n\
" + DotRadius * 2 + " 0 m\n\
" + DotRadius * 2 + " " + DotRadius * 2 * c + " " + DotRadius * 2 * c + " " + DotRadius * 2 + " 0 " + DotRadius * 2 + " c\n\
-" + DotRadius * 2 * c + " " + DotRadius * 2 + " -" + DotRadius * 2 + " " + DotRadius * 2 * c + " -" + DotRadius * 2 + " 0 c\n\
-" + DotRadius * 2 + " -" + DotRadius * 2 * c + " -" + DotRadius * 2 * c + " -" + DotRadius * 2 + " 0 -" + DotRadius * 2 + " c\n\
" + DotRadius * 2 * c + " -" + DotRadius * 2 + " " + DotRadius * 2 + " -" + DotRadius * 2 * c + " " + DotRadius * 2 + " 0 c\n\
            f\n\
            Q\n";
                  xobj.stream = stream;
                  return xobj;
              }
          },

          Cross: {
              /**
               * Creates the Actual AppearanceDictionary-References
               * @param name
               * @returns
               */
              createAppearanceStream: function createAppearanceStream(name) {
                  var appearanceStreamContent = {
                      D: {
                          'Off': AcroForm.Appearance.RadioButton.Cross.OffPushDown
                      },
                      N: {}
                  };
                  appearanceStreamContent.N[name] = AcroForm.Appearance.RadioButton.Cross.YesNormal;
                  appearanceStreamContent.D[name] = AcroForm.Appearance.RadioButton.Cross.YesPushDown;
                  return appearanceStreamContent;
              },
              createMK: function createMK() {
                  return "<< /CA (8)>>";
              },

              YesNormal: function YesNormal(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var stream = "";
                  var cross = AcroForm.Appearance.internal.calculateCross(formObject);
                  stream += "q\n\
            1 1 " + (AcroForm.Appearance.internal.getWidth(formObject) - 2) + " " + (AcroForm.Appearance.internal.getHeight(formObject) - 2) + " re\n\
            W\n\
            n\n\
            " + cross.x1.x + " " + cross.x1.y + " m\n\
            " + cross.x2.x + " " + cross.x2.y + " l\n\
            " + cross.x4.x + " " + cross.x4.y + " m\n\
            " + cross.x3.x + " " + cross.x3.y + " l\n\
            s\n\
            Q\n";
                  xobj.stream = stream;
                  return xobj;
              },
              YesPushDown: function YesPushDown(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var cross = AcroForm.Appearance.internal.calculateCross(formObject);
                  var stream = "";
                  stream += "0.749023 g\n\
            0 0 " + AcroForm.Appearance.internal.getWidth(formObject) + " " + AcroForm.Appearance.internal.getHeight(formObject) + " re\n\
            f\n\
            q\n\
            1 1 " + (AcroForm.Appearance.internal.getWidth(formObject) - 2) + " " + (AcroForm.Appearance.internal.getHeight(formObject) - 2) + " re\n\
            W\n\
            n\n\
            " + cross.x1.x + " " + cross.x1.y + " m\n\
            " + cross.x2.x + " " + cross.x2.y + " l\n\
            " + cross.x4.x + " " + cross.x4.y + " m\n\
            " + cross.x3.x + " " + cross.x3.y + " l\n\
            s\n\
            Q\n";
                  xobj.stream = stream;
                  return xobj;
              },
              OffPushDown: function OffPushDown(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var stream = "";
                  stream += "0.749023 g\n\
            0 0 " + AcroForm.Appearance.internal.getWidth(formObject) + " " + AcroForm.Appearance.internal.getHeight(formObject) + " re\n\
            f\n";
                  xobj.stream = stream;
                  return xobj;
              }
          }
      },

      /**
       * Returns the standard Appearance
       * @returns {AcroForm.FormXObject}
       */
      createDefaultAppearanceStream: function createDefaultAppearanceStream(formObject) {
          var stream = "";
          // Set Helvetica to Standard Font (size: auto)
          // Color: Black
          stream += "/Helv 0 Tf 0 g";
          return stream;
      }
  };

  AcroForm.Appearance.internal = {
      Bezier_C: 0.551915024494,

      calculateCross: function calculateCross(formObject) {
          var min = function min(x, y) {
              return x > y ? y : x;
          };

          var width = AcroForm.Appearance.internal.getWidth(formObject);
          var height = AcroForm.Appearance.internal.getHeight(formObject);
          var a = min(width, height);
          var crossSize = a;
          var borderPadding = 2; // The Padding in px


          var cross = {
              x1: { // upperLeft
                  x: (width - a) / 2,
                  y: (height - a) / 2 + a },
              x2: { // lowerRight
                  x: (width - a) / 2 + a,
                  y: (height - a) / 2 //borderPadding
              },
              x3: { // lowerLeft
                  x: (width - a) / 2,
                  y: (height - a) / 2 //borderPadding
              },
              x4: { // upperRight
                  x: (width - a) / 2 + a,
                  y: (height - a) / 2 + a }
          };

          return cross;
      }
  };
  AcroForm.Appearance.internal.getWidth = function (formObject) {
      return formObject.Rect[2]; //(formObject.Rect[2] - formObject.Rect[0]) || 0;
  };
  AcroForm.Appearance.internal.getHeight = function (formObject) {
      return formObject.Rect[3]; //(formObject.Rect[1] - formObject.Rect[3]) || 0;
  };

  // ##########################

  //### For inheritance:
  AcroForm.internal.inherit = function (child, parent) {
      var ObjectCreate = Object.create || function (o) {
          var F = function F() {};
          F.prototype = o;
          return new F();
      };
      child.prototype = Object.create(parent.prototype);
      child.prototype.constructor = child;
  };

  // ### Handy Functions:

  AcroForm.internal.arrayToPdfArray = function (array) {
      if (Array.isArray(array)) {
          var content = ' [';
          for (var i in array) {
              var element = array[i].toString();
              content += element;
              content += i < array.length - 1 ? ' ' : '';
          }
          content += ']';

          return content;
      }
  };

  AcroForm.internal.toPdfString = function (string) {
      string = string || "";

      // put Bracket at the Beginning of the String
      if (string.indexOf('(') !== 0) {
          string = '(' + string;
      }

      if (string.substring(string.length - 1) != ')') {
          string += '(';
      }
      return string;
  };

  // ##########################
  //          Classes
  // ##########################


  AcroForm.PDFObject = function () {
      // The Object ID in the PDF Object Model
      // todo
      var _objId;
      Object.defineProperty(this, 'objId', {
          get: function get() {
              if (!_objId) {
                  if (this.internal) {
                      _objId = this.internal.newObjectDeferred();
                  } else if (jsPDF.API.acroformPlugin.internal) {
                      // todo - find better option, that doesn't rely on a Global Static var
                      _objId = jsPDF.API.acroformPlugin.internal.newObjectDeferred();
                  }
              }
              if (!_objId) {
                  console.log("Couldn't create Object ID");
              }
              return _objId;
          },
          configurable: false
      });
  };

  AcroForm.PDFObject.prototype.toString = function () {
      return this.objId + " 0 R";
  };

  AcroForm.PDFObject.prototype.getString = function () {
      var res = this.objId + " 0 obj\n<<";
      var content = this.getContent();

      res += content + ">>\n";
      if (this.stream) {
          res += "stream\n";
          res += this.stream;
          res += "endstream\n";
      }
      res += "endobj\n";
      return res;
  };

  AcroForm.PDFObject.prototype.getContent = function () {
      /**
       * Prints out all enumerable Variables from the Object
       * @param fieldObject
       * @returns {string}
       */
      var createContentFromFieldObject = function createContentFromFieldObject(fieldObject) {
          var content = '';

          var keys = Object.keys(fieldObject).filter(function (key) {
              return key != 'content' && key != 'appearanceStreamContent' && key.substring(0, 1) != "_";
          });

          for (var i in keys) {
              var key = keys[i];
              var value = fieldObject[key];

              /*if (key == 'Rect' && value) {
               value = AcroForm.internal.calculateCoordinates.call(jsPDF.API.acroformPlugin.internal, value);
               }*/

              if (value) {
                  if (Array.isArray(value)) {
                      content += '/' + key + ' ' + AcroForm.internal.arrayToPdfArray(value) + "\n";
                  } else if (value instanceof AcroForm.PDFObject) {
                      // In case it is a reference to another PDFObject, take the referennce number
                      content += '/' + key + ' ' + value.objId + " 0 R" + "\n";
                  } else {
                      content += '/' + key + ' ' + value + '\n';
                  }
              }
          }
          return content;
      };

      var object = "";

      object += createContentFromFieldObject(this);
      return object;
  };

  AcroForm.FormXObject = function () {
      AcroForm.PDFObject.call(this);
      this.Type = "/XObject";
      this.Subtype = "/Form";
      this.FormType = 1;
      this.BBox;
      this.Matrix;
      this.Resources = "2 0 R";
      this.PieceInfo;
      var _stream;
      Object.defineProperty(this, 'Length', {
          enumerable: true,
          get: function get() {
              return _stream !== undefined ? _stream.length : 0;
          }
      });
      Object.defineProperty(this, 'stream', {
          enumerable: false,
          set: function set(val) {
              _stream = val;
          },
          get: function get() {
              if (_stream) {
                  return _stream;
              } else {
                  return null;
              }
          }
      });
  };

  AcroForm.internal.inherit(AcroForm.FormXObject, AcroForm.PDFObject);

  AcroForm.AcroFormDictionary = function () {
      AcroForm.PDFObject.call(this);
      var _Kids = [];
      Object.defineProperty(this, 'Kids', {
          enumerable: false,
          configurable: true,
          get: function get() {
              if (_Kids.length > 0) {
                  return _Kids;
              } else {
                  return;
              }
          }
      });
      Object.defineProperty(this, 'Fields', {
          enumerable: true,
          configurable: true,
          get: function get() {
              return _Kids;
          }
      });
      // Default Appearance
      this.DA;
  };

  AcroForm.internal.inherit(AcroForm.AcroFormDictionary, AcroForm.PDFObject);

  // ##### The Objects, the User can Create:


  // The Field Object contains the Variables, that every Field needs
  // Rectangle for Appearance: lower_left_X, lower_left_Y, width, height
  AcroForm.Field = function () {
      'use strict';

      AcroForm.PDFObject.call(this);

      var _Rect;
      Object.defineProperty(this, 'Rect', {
          enumerable: true,
          configurable: false,
          get: function get() {
              if (!_Rect) {
                  return;
              }
              var tmp = _Rect;
              //var calculatedRes = AcroForm.internal.calculateCoordinates(_Rect); // do later!
              return tmp;
          },
          set: function set(val) {
              _Rect = val;
          }
      });

      var _FT = "";
      Object.defineProperty(this, 'FT', {
          enumerable: true,
          set: function set(val) {
              _FT = val;
          },
          get: function get() {
              return _FT;
          }
      });
      /**
       * The Partial name of the Field Object.
       * It has to be unique.
       */
      var _T;

      Object.defineProperty(this, 'T', {
          enumerable: true,
          configurable: false,
          set: function set(val) {
              _T = val;
          },
          get: function get() {
              if (!_T || _T.length < 1) {
                  if (this instanceof AcroForm.ChildClass) {
                      // In case of a Child from a Radio´Group, you don't need a FieldName!!!
                      return;
                  }
                  return "(FieldObject" + AcroForm.Field.FieldNum++ + ")";
              }
              if (_T.substring(0, 1) == "(" && _T.substring(_T.length - 1)) {
                  return _T;
              }
              return "(" + _T + ")";
          }
      });

      var _DA;
      // Defines the default appearance (Needed for variable Text)
      Object.defineProperty(this, 'DA', {
          enumerable: true,
          get: function get() {
              if (!_DA) {
                  return;
              }
              return '(' + _DA + ')';
          },
          set: function set(val) {
              _DA = val;
          }
      });

      var _DV;
      // Defines the default value
      Object.defineProperty(this, 'DV', {
          enumerable: true,
          configurable: true,
          get: function get() {
              if (!_DV) {
                  return;
              }
              return _DV;
          },
          set: function set(val) {
              _DV = val;
          }
      });

      //this.Type = "/Annot";
      //this.Subtype = "/Widget";
      Object.defineProperty(this, 'Type', {
          enumerable: true,
          get: function get() {
              return this.hasAnnotation ? "/Annot" : null;
          }
      });

      Object.defineProperty(this, 'Subtype', {
          enumerable: true,
          get: function get() {
              return this.hasAnnotation ? "/Widget" : null;
          }
      });

      /**
       *
       * @type {Array}
       */
      this.BG;

      Object.defineProperty(this, 'hasAnnotation', {
          enumerable: false,
          get: function get() {
              if (this.Rect || this.BC || this.BG) {
                  return true;
              }
              return false;
          }
      });

      Object.defineProperty(this, 'hasAppearanceStream', {
          enumerable: false,
          configurable: true,
          writable: true
      });

      Object.defineProperty(this, 'page', {
          enumerable: false,
          configurable: true,
          writable: true
      });
  };
  AcroForm.Field.FieldNum = 0;

  AcroForm.internal.inherit(AcroForm.Field, AcroForm.PDFObject);

  AcroForm.ChoiceField = function () {
      AcroForm.Field.call(this);
      // Field Type = Choice Field
      this.FT = "/Ch";
      // options
      this.Opt = [];
      this.V = '()';
      // Top Index
      this.TI = 0;
      /**
       * Defines, whether the
       * @type {boolean}
       */
      this.combo = false;
      /**
       * Defines, whether the Choice Field is an Edit Field.
       * An Edit Field is automatically an Combo Field.
       */
      Object.defineProperty(this, 'edit', {
          enumerable: true,
          set: function set(val) {
              if (val == true) {
                  this._edit = true;
                  // ComboBox has to be true
                  this.combo = true;
              } else {
                  this._edit = false;
              }
          },
          get: function get() {
              if (!this._edit) {
                  return false;
              }
              return this._edit;
          },
          configurable: false
      });
      this.hasAppearanceStream = true;
      Object.defineProperty(this, 'V', {
          get: function get() {
              AcroForm.internal.toPdfString();
          }
      });
  };
  AcroForm.internal.inherit(AcroForm.ChoiceField, AcroForm.Field);
  window["ChoiceField"] = AcroForm.ChoiceField;

  AcroForm.ListBox = function () {
      AcroForm.ChoiceField.call(this);
      //var combo = true;
  };
  AcroForm.internal.inherit(AcroForm.ListBox, AcroForm.ChoiceField);
  window["ListBox"] = AcroForm.ListBox;

  AcroForm.ComboBox = function () {
      AcroForm.ListBox.call(this);
      this.combo = true;
  };
  AcroForm.internal.inherit(AcroForm.ComboBox, AcroForm.ListBox);
  window["ComboBox"] = AcroForm.ComboBox;

  AcroForm.EditBox = function () {
      AcroForm.ComboBox.call(this);
      this.edit = true;
  };
  AcroForm.internal.inherit(AcroForm.EditBox, AcroForm.ComboBox);
  window["EditBox"] = AcroForm.EditBox;

  AcroForm.Button = function () {
      AcroForm.Field.call(this);
      this.FT = "/Btn";
      //this.hasAnnotation = true;
  };
  AcroForm.internal.inherit(AcroForm.Button, AcroForm.Field);
  window["Button"] = AcroForm.Button;

  AcroForm.PushButton = function () {
      AcroForm.Button.call(this);
      this.pushbutton = true;
  };
  AcroForm.internal.inherit(AcroForm.PushButton, AcroForm.Button);
  window["PushButton"] = AcroForm.PushButton;

  AcroForm.RadioButton = function () {
      AcroForm.Button.call(this);
      this.radio = true;
      var _Kids = [];
      Object.defineProperty(this, 'Kids', {
          enumerable: true,
          get: function get() {
              if (_Kids.length > 0) {
                  return _Kids;
              }
          }
      });

      Object.defineProperty(this, '__Kids', {
          get: function get() {
              return _Kids;
          }
      });

      var _noToggleToOff;

      Object.defineProperty(this, 'noToggleToOff', {
          enumerable: false,
          get: function get() {
              return _noToggleToOff;
          },
          set: function set(val) {
              _noToggleToOff = val;
          }
      });

      //this.hasAnnotation = false;
  };
  AcroForm.internal.inherit(AcroForm.RadioButton, AcroForm.Button);
  window["RadioButton"] = AcroForm.RadioButton;

  /*
   * The Child classs of a RadioButton (the radioGroup)
   * -> The single Buttons
   */
  AcroForm.ChildClass = function (parent, name) {
      AcroForm.Field.call(this);
      this.Parent = parent;

      // todo: set AppearanceType as variable that can be set from the outside...
      this._AppearanceType = AcroForm.Appearance.RadioButton.Circle; // The Default appearanceType is the Circle
      this.appearanceStreamContent = this._AppearanceType.createAppearanceStream(name);

      // Set Print in the Annot Flag
      this.F = AcroForm.internal.setBitPosition(this.F, 3, 1);

      // Set AppearanceCharacteristicsDictionary with default appearance if field is not interacting with user
      this.MK = this._AppearanceType.createMK(); // (8) -> Cross, (1)-> Circle, ()-> nothing

      // Default Appearance is Off
      this.AS = "/Off"; // + name;

      this._Name = name;
  };
  AcroForm.internal.inherit(AcroForm.ChildClass, AcroForm.Field);

  AcroForm.RadioButton.prototype.setAppearance = function (appearance) {
      if (!('createAppearanceStream' in appearance && 'createMK' in appearance)) {
          console.log("Couldn't assign Appearance to RadioButton. Appearance was Invalid!");
          return;
      }
      for (var i in this.__Kids) {
          var child = this.__Kids[i];

          child.appearanceStreamContent = appearance.createAppearanceStream(child._Name);
          child.MK = appearance.createMK();
      }
  };

  AcroForm.RadioButton.prototype.createOption = function (name) {
      var parent = this;
      var kidCount = this.__Kids.length;

      // Create new Child for RadioGroup
      var child = new AcroForm.ChildClass(parent, name);
      // Add to Parent
      this.__Kids.push(child);

      jsPDF.API.addField(child);

      return child;
  };

  AcroForm.CheckBox = function () {
      Button.call(this);
      this.appearanceStreamContent = AcroForm.Appearance.CheckBox.createAppearanceStream();
      this.MK = AcroForm.Appearance.CheckBox.createMK();
      this.AS = "/On";
      this.V = "/On";
  };
  AcroForm.internal.inherit(AcroForm.CheckBox, AcroForm.Button);
  window["CheckBox"] = AcroForm.CheckBox;

  AcroForm.TextField = function () {
      AcroForm.Field.call(this);
      this.DA = AcroForm.Appearance.createDefaultAppearanceStream();
      this.F = 4;
      var _V;
      Object.defineProperty(this, 'V', {
          get: function get() {
              if (_V) {
                  return "(" + _V + ")";
              } else {
                  return _V;
              }
          },
          enumerable: true,
          set: function set(val) {
              _V = val;
          }
      });

      var _DV;
      Object.defineProperty(this, 'DV', {
          get: function get() {
              if (_DV) {
                  return "(" + _DV + ")";
              } else {
                  return _DV;
              }
          },
          enumerable: true,
          set: function set(val) {
              _DV = val;
          }
      });

      var _multiline = false;
      Object.defineProperty(this, 'multiline', {
          enumerable: false,
          get: function get() {
              return _multiline;
          },
          set: function set(val) {
              _multiline = val;
          }
      });

      //this.multiline = false;
      //this.password = false;
      /**
       * For PDF 1.4
       * @type {boolean}
       */
      //this.fileSelect = false;
      /**
       * For PDF 1.4
       * @type {boolean}
       */
      //this.doNotSpellCheck = false;
      /**
       * For PDF 1.4
       * @type {boolean}
       */
      //this.doNotScroll = false;

      var _MaxLen = false;
      Object.defineProperty(this, 'MaxLen', {
          enumerable: true,
          get: function get() {
              return _MaxLen;
          },
          set: function set(val) {
              _MaxLen = val;
          }
      });

      Object.defineProperty(this, 'hasAppearanceStream', {
          enumerable: false,
          get: function get() {
              return this.V || this.DV;
          }
      });
  };
  AcroForm.internal.inherit(AcroForm.TextField, AcroForm.Field);
  window["TextField"] = AcroForm.TextField;

  AcroForm.PasswordField = function () {
      TextField.call(this);
      Object.defineProperty(this, 'password', {
          value: true,
          enumerable: false,
          configurable: false,
          writable: false
      });
  };
  AcroForm.internal.inherit(AcroForm.PasswordField, AcroForm.TextField);
  window["PasswordField"] = AcroForm.PasswordField;

  // ############ internal functions

  /*
   * small workaround for calculating the TextMetric approximately
   * @param text
   * @param fontsize
   * @returns {TextMetrics} (Has Height and Width)
   */
  AcroForm.internal.calculateFontSpace = function (text, fontsize, fonttype) {
      var fonttype = fonttype || "helvetica";
      //re-use canvas object for speed improvements
      var canvas = AcroForm.internal.calculateFontSpace.canvas || (AcroForm.internal.calculateFontSpace.canvas = document.createElement('canvas'));

      var context = canvas.getContext('2d');
      context.save();
      var newFont = fontsize + " " + fonttype;
      context.font = newFont;
      var res = context.measureText(text);
      context.fontcolor = 'black';
      // Calculate height:
      var context = canvas.getContext('2d');
      res.height = context.measureText("3").width * 1.5; // 3 because in ZapfDingbats its a Hook and a 3 in normal fonts
      context.restore();

      var width = res.width;

      return res;
  };

  AcroForm.internal.calculateX = function (formObject, text, font, maxFontSize) {
      var maxFontSize = maxFontSize || 12;
      var font = font || "helvetica";
      var returnValue = {
          text: "",
          fontSize: ""
      };
      // Remove Brackets
      text = text.substr(0, 1) == '(' ? text.substr(1) : text;
      text = text.substr(text.length - 1) == ')' ? text.substr(0, text.length - 1) : text;
      // split into array of words
      var textSplit = text.split(' ');

      /**
       * the color could be ((alpha)||(r,g,b)||(c,m,y,k))
       * @type {string}
       */
      var color = "0 g\n";
      var fontSize = maxFontSize; // The Starting fontSize (The Maximum)
      var lineSpacing = 2;
      var borderPadding = 2;

      var height = AcroForm.Appearance.internal.getHeight(formObject) || 0;
      height = height < 0 ? -height : height;
      var width = AcroForm.Appearance.internal.getWidth(formObject) || 0;
      width = width < 0 ? -width : width;

      var isSmallerThanWidth = function isSmallerThanWidth(i, lastLine, fontSize) {
          if (i + 1 < textSplit.length) {
              var tmp = lastLine + " " + textSplit[i + 1];
              var TextWidth = AcroForm.internal.calculateFontSpace(tmp, fontSize + "px", font).width;
              var FieldWidth = width - 2 * borderPadding;
              return TextWidth <= FieldWidth;
          } else {
              return false;
          }
      };

      fontSize++;
      FontSize: while (true) {
          var text = "";
          fontSize--;
          var textHeight = AcroForm.internal.calculateFontSpace("3", fontSize + "px", font).height;
          var startY = formObject.multiline ? height - fontSize : (height - textHeight) / 2;
          startY += lineSpacing;
          var startX = -borderPadding;

          var lastX = startX,
              lastY = startY;
          var firstWordInLine = 0,
              lastWordInLine = 0;
          var lastLength = 0;

          var y = 0;
          if (fontSize == 0) {
              // In case, the Text doesn't fit at all
              fontSize = 12;
              text = "(...) Tj\n";
              text += "% Width of Text: " + AcroForm.internal.calculateFontSpace(text, "1px").width + ", FieldWidth:" + width + "\n";
              break;
          }

          lastLength = AcroForm.internal.calculateFontSpace(textSplit[0] + " ", fontSize + "px", font).width;

          var lastLine = "";
          var lineCount = 0;
          Line: for (var i in textSplit) {
              lastLine += textSplit[i] + " ";
              // Remove last blank
              lastLine = lastLine.substr(lastLine.length - 1) == " " ? lastLine.substr(0, lastLine.length - 1) : lastLine;
              var key = parseInt(i);
              lastLength = AcroForm.internal.calculateFontSpace(lastLine + " ", fontSize + "px", font).width;
              var nextLineIsSmaller = isSmallerThanWidth(key, lastLine, fontSize);
              var isLastWord = i >= textSplit.length - 1;
              if (nextLineIsSmaller && !isLastWord) {
                  lastLine += " ";
                  continue; // Line
              } else if (!nextLineIsSmaller && !isLastWord) {
                  if (!formObject.multiline) {
                      continue FontSize;
                  } else {
                      if ((textHeight + lineSpacing) * (lineCount + 2) + lineSpacing > height) {
                          // If the Text is higher than the FieldObject
                          continue FontSize;
                      }
                      lastWordInLine = key;
                      // go on
                  }
              } else if (isLastWord) {
                  lastWordInLine = key;
              } else {
                  if (formObject.multiline && (textHeight + lineSpacing) * (lineCount + 2) + lineSpacing > height) {
                      // If the Text is higher than the FieldObject
                      continue FontSize;
                  }
              }

              var line = '';

              for (var x = firstWordInLine; x <= lastWordInLine; x++) {
                  line += textSplit[x] + ' ';
              }

              // Remove last blank
              line = line.substr(line.length - 1) == " " ? line.substr(0, line.length - 1) : line;
              //lastLength -= blankSpace.width;
              lastLength = AcroForm.internal.calculateFontSpace(line, fontSize + "px", font).width;

              // Calculate startX
              switch (formObject.Q) {
                  case 2:
                      // Right justified
                      startX = width - lastLength - borderPadding;
                      break;
                  case 1:
                      // Q = 1 := Text-Alignment: Center
                      startX = (width - lastLength) / 2;
                      break;
                  case 0:
                  default:
                      startX = borderPadding;
                      break;
              }
              text += startX + ' ' + lastY + ' Td\n';
              text += '(' + line + ') Tj\n';
              // reset X in PDF
              text += -startX + ' 0 Td\n';

              // After a Line, adjust y position
              lastY = -(fontSize + lineSpacing);
              lastX = startX;

              // Reset for next iteration step
              lastLength = 0;
              firstWordInLine = lastWordInLine + 1;
              lineCount++;

              lastLine = "";
              continue Line;
          }
          break;
      }

      returnValue.text = text;
      returnValue.fontSize = fontSize;

      return returnValue;
  };

  AcroForm.internal.calculateAppearanceStream = function (formObject) {
      if (formObject.appearanceStreamContent) {
          // If appearanceStream is already set, use it
          return formObject.appearanceStreamContent;
      }

      if (!formObject.V && !formObject.DV) {
          return;
      }

      // else calculate it

      var stream = '';

      var text = formObject.V || formObject.DV;

      var calcRes = AcroForm.internal.calculateX(formObject, text);

      stream += '/Tx BMC\n' + 'q\n' +
      //color + '\n' +
      '/F1 ' + calcRes.fontSize + ' Tf\n' +
      // Text Matrix
      '1 0 0 1 0 0 Tm\n';
      // Begin Text
      stream += 'BT\n';
      stream += calcRes.text;
      // End Text
      stream += 'ET\n';
      stream += 'Q\n' + 'EMC\n';

      var appearanceStreamContent = new AcroForm.createFormXObject(formObject);

      appearanceStreamContent.stream = stream;

      var appearance = {
          N: {
              'Normal': appearanceStreamContent
          }
      };

      return appearanceStreamContent;
  };

  /*
   * Converts the Parameters from x,y,w,h to lowerLeftX, lowerLeftY, upperRightX, upperRightY
   * @param x
   * @param y
   * @param w
   * @param h
   * @returns {*[]}
   */
  AcroForm.internal.calculateCoordinates = function (x, y, w, h) {
      var coordinates = {};

      if (this.internal) {
          var mmtopx = function mmtopx(x) {
              return x * this.internal.scaleFactor;
          };

          if (Array.isArray(x)) {
              x[0] = AcroForm.scale(x[0]);
              x[1] = AcroForm.scale(x[1]);
              x[2] = AcroForm.scale(x[2]);
              x[3] = AcroForm.scale(x[3]);

              coordinates.lowerLeft_X = x[0] || 0;
              coordinates.lowerLeft_Y = mmtopx.call(this, this.internal.pageSize.height) - x[3] - x[1] || 0;
              coordinates.upperRight_X = x[0] + x[2] || 0;
              coordinates.upperRight_Y = mmtopx.call(this, this.internal.pageSize.height) - x[1] || 0;
          } else {
              x = AcroForm.scale(x);
              y = AcroForm.scale(y);
              w = AcroForm.scale(w);
              h = AcroForm.scale(h);
              coordinates.lowerLeft_X = x || 0;
              coordinates.lowerLeft_Y = this.internal.pageSize.height - y || 0;
              coordinates.upperRight_X = x + w || 0;
              coordinates.upperRight_Y = this.internal.pageSize.height - y + h || 0;
          }
      } else {
          // old method, that is fallback, if we can't get the pageheight, the coordinate-system starts from lower left
          if (Array.isArray(x)) {
              coordinates.lowerLeft_X = x[0] || 0;
              coordinates.lowerLeft_Y = x[1] || 0;
              coordinates.upperRight_X = x[0] + x[2] || 0;
              coordinates.upperRight_Y = x[1] + x[3] || 0;
          } else {
              coordinates.lowerLeft_X = x || 0;
              coordinates.lowerLeft_Y = y || 0;
              coordinates.upperRight_X = x + w || 0;
              coordinates.upperRight_Y = y + h || 0;
          }
      }

      return [coordinates.lowerLeft_X, coordinates.lowerLeft_Y, coordinates.upperRight_X, coordinates.upperRight_Y];
  };

  AcroForm.internal.calculateColor = function (r, g, b) {
      var color = new Array(3);
      color.r = r | 0;
      color.g = g | 0;
      color.b = b | 0;
      return color;
  };

  AcroForm.internal.getBitPosition = function (variable, position) {
      variable = variable || 0;
      var bitMask = 1;
      bitMask = bitMask << position - 1;
      return variable | bitMask;
  };

  AcroForm.internal.setBitPosition = function (variable, position, value) {
      variable = variable || 0;
      value = value || 1;

      var bitMask = 1;
      bitMask = bitMask << position - 1;

      if (value == 1) {
          // Set the Bit to 1
          var variable = variable | bitMask;
      } else {
          // Set the Bit to 0
          var variable = variable & ~bitMask;
      }

      return variable;
  };

  /**
   * jsPDF addHTML PlugIn
   * Copyright (c) 2014 Diego Casorran
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  (function (jsPDFAPI) {
  	'use strict';

  	/**
    * Renders an HTML element to canvas object which added to the PDF
    *
    * This PlugIn requires html2canvas: https://github.com/niklasvh/html2canvas
    *            OR rasterizeHTML: https://github.com/cburgmer/rasterizeHTML.js
    *
    * @public
    * @function
    * @param element {Mixed} HTML Element, or anything supported by html2canvas.
    * @param x {Number} starting X coordinate in jsPDF instance's declared units.
    * @param y {Number} starting Y coordinate in jsPDF instance's declared units.
    * @param options {Object} Additional options, check the code below.
    * @param callback {Function} to call when the rendering has finished.
    *
    * NOTE: Every parameter is optional except 'element' and 'callback', in such
    *       case the image is positioned at 0x0 covering the whole PDF document
    *       size. Ie, to easily take screenshots of webpages saving them to PDF.
    */

  	jsPDFAPI.addHTML = function (element, x, y, options, callback) {
  		'use strict';

  		if (typeof html2canvas === 'undefined' && typeof rasterizeHTML === 'undefined') throw new Error('You need either ' + 'https://github.com/niklasvh/html2canvas' + ' or https://github.com/cburgmer/rasterizeHTML.js');

  		if (typeof x !== 'number') {
  			options = x;
  			callback = y;
  		}

  		if (typeof options === 'function') {
  			callback = options;
  			options = null;
  		}

  		var I = this.internal,
  		    K = I.scaleFactor,
  		    W = I.pageSize.width,
  		    H = I.pageSize.height;

  		options = options || {};
  		options.onrendered = function (obj) {
  			x = parseInt(x) || 0;
  			y = parseInt(y) || 0;
  			var dim = options.dim || {};
  			var h = dim.h || 0;
  			var w = dim.w || Math.min(W, obj.width / K) - x;

  			var format = 'JPEG';
  			if (options.format) format = options.format;

  			if (obj.height > H && options.pagesplit) {
  				var crop = function () {
  					var cy = 0;
  					while (1) {
  						var canvas = document.createElement('canvas');
  						canvas.width = Math.min(W * K, obj.width);
  						canvas.height = Math.min(H * K, obj.height - cy);
  						var ctx = canvas.getContext('2d');
  						ctx.drawImage(obj, 0, cy, obj.width, canvas.height, 0, 0, canvas.width, canvas.height);
  						var args = [canvas, x, cy ? 0 : y, canvas.width / K, canvas.height / K, format, null, 'SLOW'];
  						this.addImage.apply(this, args);
  						cy += canvas.height;
  						if (cy >= obj.height) break;
  						this.addPage();
  					}
  					callback(w, cy, null, args);
  				}.bind(this);
  				if (obj.nodeName === 'CANVAS') {
  					var img = new Image();
  					img.onload = crop;
  					img.src = obj.toDataURL("image/png");
  					obj = img;
  				} else {
  					crop();
  				}
  			} else {
  				var alias = Math.random().toString(35);
  				var args = [obj, x, y, w, h, format, alias, 'SLOW'];

  				this.addImage.apply(this, args);

  				callback(w, h, alias, args);
  			}
  		}.bind(this);

  		if (typeof html2canvas !== 'undefined' && !options.rstz) {
  			return html2canvas(element, options);
  		}

  		if (typeof rasterizeHTML !== 'undefined') {
  			var meth = 'drawDocument';
  			if (typeof element === 'string') {
  				meth = /^http/.test(element) ? 'drawURL' : 'drawHTML';
  			}
  			options.width = options.width || W * K;
  			return rasterizeHTML[meth](element, void 0, options).then(function (r) {
  				options.onrendered(r.image);
  			}, function (e) {
  				callback(null, e);
  			});
  		}

  		return null;
  	};
  })(jsPDF.API);

  /** @preserve
   * jsPDF addImage plugin
   * Copyright (c) 2012 Jason Siefken, https://github.com/siefkenj/
   *               2013 Chris Dowling, https://github.com/gingerchris
   *               2013 Trinh Ho, https://github.com/ineedfat
   *               2013 Edwin Alejandro Perez, https://github.com/eaparango
   *               2013 Norah Smith, https://github.com/burnburnrocket
   *               2014 Diego Casorran, https://github.com/diegocr
   *               2014 James Robb, https://github.com/jamesbrobb
   *
   * 
   */

  ;(function (jsPDFAPI) {
  	'use strict';

  	var namespace = 'addImage_',
  	    supported_image_types = ['jpeg', 'jpg', 'png'];

  	// Image functionality ported from pdf.js
  	var putImage = function putImage(img) {

  		var objectNumber = this.internal.newObject(),
  		    out = this.internal.write,
  		    putStream = this.internal.putStream;

  		img['n'] = objectNumber;

  		out('<</Type /XObject');
  		out('/Subtype /Image');
  		out('/Width ' + img['w']);
  		out('/Height ' + img['h']);
  		if (img['cs'] === this.color_spaces.INDEXED) {
  			out('/ColorSpace [/Indexed /DeviceRGB '
  			// if an indexed png defines more than one colour with transparency, we've created a smask
  			+ (img['pal'].length / 3 - 1) + ' ' + ('smask' in img ? objectNumber + 2 : objectNumber + 1) + ' 0 R]');
  		} else {
  			out('/ColorSpace /' + img['cs']);
  			if (img['cs'] === this.color_spaces.DEVICE_CMYK) {
  				out('/Decode [1 0 1 0 1 0 1 0]');
  			}
  		}
  		out('/BitsPerComponent ' + img['bpc']);
  		if ('f' in img) {
  			out('/Filter /' + img['f']);
  		}
  		if ('dp' in img) {
  			out('/DecodeParms <<' + img['dp'] + '>>');
  		}
  		if ('trns' in img && img['trns'].constructor == Array) {
  			var trns = '',
  			    i = 0,
  			    len = img['trns'].length;
  			for (; i < len; i++) {
  				trns += img['trns'][i] + ' ' + img['trns'][i] + ' ';
  			}out('/Mask [' + trns + ']');
  		}
  		if ('smask' in img) {
  			out('/SMask ' + (objectNumber + 1) + ' 0 R');
  		}
  		out('/Length ' + img['data'].length + '>>');

  		putStream(img['data']);

  		out('endobj');

  		// Soft mask
  		if ('smask' in img) {
  			var dp = '/Predictor ' + img['p'] + ' /Colors 1 /BitsPerComponent ' + img['bpc'] + ' /Columns ' + img['w'];
  			var smask = { 'w': img['w'], 'h': img['h'], 'cs': 'DeviceGray', 'bpc': img['bpc'], 'dp': dp, 'data': img['smask'] };
  			if ('f' in img) smask.f = img['f'];
  			putImage.call(this, smask);
  		}

  		//Palette
  		if (img['cs'] === this.color_spaces.INDEXED) {

  			this.internal.newObject();
  			//out('<< /Filter / ' + img['f'] +' /Length ' + img['pal'].length + '>>');
  			//putStream(zlib.compress(img['pal']));
  			out('<< /Length ' + img['pal'].length + '>>');
  			putStream(this.arrayBufferToBinaryString(new Uint8Array(img['pal'])));
  			out('endobj');
  		}
  	},
  	    putResourcesCallback = function putResourcesCallback() {
  		var images = this.internal.collections[namespace + 'images'];
  		for (var i in images) {
  			putImage.call(this, images[i]);
  		}
  	},
  	    putXObjectsDictCallback = function putXObjectsDictCallback() {
  		var images = this.internal.collections[namespace + 'images'],
  		    out = this.internal.write,
  		    image;
  		for (var i in images) {
  			image = images[i];
  			out('/I' + image['i'], image['n'], '0', 'R');
  		}
  	},
  	    checkCompressValue = function checkCompressValue(value) {
  		if (value && typeof value === 'string') value = value.toUpperCase();
  		return value in jsPDFAPI.image_compression ? value : jsPDFAPI.image_compression.NONE;
  	},
  	    getImages = function getImages() {
  		var images = this.internal.collections[namespace + 'images'];
  		//first run, so initialise stuff
  		if (!images) {
  			this.internal.collections[namespace + 'images'] = images = {};
  			this.internal.events.subscribe('putResources', putResourcesCallback);
  			this.internal.events.subscribe('putXobjectDict', putXObjectsDictCallback);
  		}

  		return images;
  	},
  	    getImageIndex = function getImageIndex(images) {
  		var imageIndex = 0;

  		if (images) {
  			// this is NOT the first time this method is ran on this instance of jsPDF object.
  			imageIndex = Object.keys ? Object.keys(images).length : function (o) {
  				var i = 0;
  				for (var e in o) {
  					if (o.hasOwnProperty(e)) {
  						i++;
  					}
  				}
  				return i;
  			}(images);
  		}

  		return imageIndex;
  	},
  	    notDefined = function notDefined(value) {
  		return typeof value === 'undefined' || value === null;
  	},
  	    generateAliasFromData = function generateAliasFromData(data) {
  		return typeof data === 'string' && jsPDFAPI.sHashCode(data);
  	},
  	    doesNotSupportImageType = function doesNotSupportImageType(type) {
  		return supported_image_types.indexOf(type) === -1;
  	},
  	    processMethodNotEnabled = function processMethodNotEnabled(type) {
  		return typeof jsPDFAPI['process' + type.toUpperCase()] !== 'function';
  	},
  	    isDOMElement = function isDOMElement(object) {
  		return (typeof object === 'undefined' ? 'undefined' : _typeof(object)) === 'object' && object.nodeType === 1;
  	},
  	    createDataURIFromElement = function createDataURIFromElement(element, format, angle) {

  		//if element is an image which uses data url definition, just return the dataurl
  		if (element.nodeName === 'IMG' && element.hasAttribute('src')) {
  			var src = '' + element.getAttribute('src');
  			if (!angle && src.indexOf('data:image/') === 0) return src;

  			// only if the user doesn't care about a format
  			if (!format && /\.png(?:[?#].*)?$/i.test(src)) format = 'png';
  		}

  		if (element.nodeName === 'CANVAS') {
  			var canvas = element;
  		} else {
  			var canvas = document.createElement('canvas');
  			canvas.width = element.clientWidth || element.width;
  			canvas.height = element.clientHeight || element.height;

  			var ctx = canvas.getContext('2d');
  			if (!ctx) {
  				throw 'addImage requires canvas to be supported by browser.';
  			}
  			if (angle) {
  				var x,
  				    y,
  				    b,
  				    c,
  				    s,
  				    w,
  				    h,
  				    to_radians = Math.PI / 180,
  				    angleInRadians;

  				if ((typeof angle === 'undefined' ? 'undefined' : _typeof(angle)) === 'object') {
  					x = angle.x;
  					y = angle.y;
  					b = angle.bg;
  					angle = angle.angle;
  				}
  				angleInRadians = angle * to_radians;
  				c = Math.abs(Math.cos(angleInRadians));
  				s = Math.abs(Math.sin(angleInRadians));
  				w = canvas.width;
  				h = canvas.height;
  				canvas.width = h * s + w * c;
  				canvas.height = h * c + w * s;

  				if (isNaN(x)) x = canvas.width / 2;
  				if (isNaN(y)) y = canvas.height / 2;

  				ctx.clearRect(0, 0, canvas.width, canvas.height);
  				ctx.fillStyle = b || 'white';
  				ctx.fillRect(0, 0, canvas.width, canvas.height);
  				ctx.save();
  				ctx.translate(x, y);
  				ctx.rotate(angleInRadians);
  				ctx.drawImage(element, -(w / 2), -(h / 2));
  				ctx.rotate(-angleInRadians);
  				ctx.translate(-x, -y);
  				ctx.restore();
  			} else {
  				ctx.drawImage(element, 0, 0, canvas.width, canvas.height);
  			}
  		}
  		return canvas.toDataURL(('' + format).toLowerCase() == 'png' ? 'image/png' : 'image/jpeg');
  	},
  	    checkImagesForAlias = function checkImagesForAlias(alias, images) {
  		var cached_info;
  		if (images) {
  			for (var e in images) {
  				if (alias === images[e].alias) {
  					cached_info = images[e];
  					break;
  				}
  			}
  		}
  		return cached_info;
  	},
  	    determineWidthAndHeight = function determineWidthAndHeight(w, h, info) {
  		if (!w && !h) {
  			w = -96;
  			h = -96;
  		}
  		if (w < 0) {
  			w = -1 * info['w'] * 72 / w / this.internal.scaleFactor;
  		}
  		if (h < 0) {
  			h = -1 * info['h'] * 72 / h / this.internal.scaleFactor;
  		}
  		if (w === 0) {
  			w = h * info['w'] / info['h'];
  		}
  		if (h === 0) {
  			h = w * info['h'] / info['w'];
  		}

  		return [w, h];
  	},
  	    writeImageToPDF = function writeImageToPDF(x, y, w, h, info, index, images) {
  		var dims = determineWidthAndHeight.call(this, w, h, info),
  		    coord = this.internal.getCoordinateString,
  		    vcoord = this.internal.getVerticalCoordinateString;

  		w = dims[0];
  		h = dims[1];

  		images[index] = info;

  		this.internal.write('q', coord(w), '0 0', coord(h) // TODO: check if this should be shifted by vcoord
  		, coord(x), vcoord(y + h), 'cm /I' + info['i'], 'Do Q');
  	};

  	/**
    * COLOR SPACES
    */
  	jsPDFAPI.color_spaces = {
  		DEVICE_RGB: 'DeviceRGB',
  		DEVICE_GRAY: 'DeviceGray',
  		DEVICE_CMYK: 'DeviceCMYK',
  		CAL_GREY: 'CalGray',
  		CAL_RGB: 'CalRGB',
  		LAB: 'Lab',
  		ICC_BASED: 'ICCBased',
  		INDEXED: 'Indexed',
  		PATTERN: 'Pattern',
  		SEPARATION: 'Separation',
  		DEVICE_N: 'DeviceN'
  	};

  	/**
    * DECODE METHODS
    */
  	jsPDFAPI.decode = {
  		DCT_DECODE: 'DCTDecode',
  		FLATE_DECODE: 'FlateDecode',
  		LZW_DECODE: 'LZWDecode',
  		JPX_DECODE: 'JPXDecode',
  		JBIG2_DECODE: 'JBIG2Decode',
  		ASCII85_DECODE: 'ASCII85Decode',
  		ASCII_HEX_DECODE: 'ASCIIHexDecode',
  		RUN_LENGTH_DECODE: 'RunLengthDecode',
  		CCITT_FAX_DECODE: 'CCITTFaxDecode'
  	};

  	/**
    * IMAGE COMPRESSION TYPES
    */
  	jsPDFAPI.image_compression = {
  		NONE: 'NONE',
  		FAST: 'FAST',
  		MEDIUM: 'MEDIUM',
  		SLOW: 'SLOW'
  	};

  	jsPDFAPI.sHashCode = function (str) {
  		return Array.prototype.reduce && str.split("").reduce(function (a, b) {
  			a = (a << 5) - a + b.charCodeAt(0);return a & a;
  		}, 0);
  	};

  	jsPDFAPI.isString = function (object) {
  		return typeof object === 'string';
  	};

  	/**
    * Strips out and returns info from a valid base64 data URI
    * @param {String[dataURI]} a valid data URI of format 'data:[<MIME-type>][;base64],<data>'
    * @returns an Array containing the following
    * [0] the complete data URI
    * [1] <MIME-type>
    * [2] format - the second part of the mime-type i.e 'png' in 'image/png'
    * [4] <data>
    */
  	jsPDFAPI.extractInfoFromBase64DataURI = function (dataURI) {
  		return (/^data:([\w]+?\/([\w]+?));base64,(.+?)$/g.exec(dataURI)
  		);
  	};

  	/**
    * Check to see if ArrayBuffer is supported
    */
  	jsPDFAPI.supportsArrayBuffer = function () {
  		return typeof ArrayBuffer !== 'undefined' && typeof Uint8Array !== 'undefined';
  	};

  	/**
    * Tests supplied object to determine if ArrayBuffer
    * @param {Object[object]}
    */
  	jsPDFAPI.isArrayBuffer = function (object) {
  		if (!this.supportsArrayBuffer()) return false;
  		return object instanceof ArrayBuffer;
  	};

  	/**
    * Tests supplied object to determine if it implements the ArrayBufferView (TypedArray) interface
    * @param {Object[object]}
    */
  	jsPDFAPI.isArrayBufferView = function (object) {
  		if (!this.supportsArrayBuffer()) return false;
  		if (typeof Uint32Array === 'undefined') return false;
  		return object instanceof Int8Array || object instanceof Uint8Array || typeof Uint8ClampedArray !== 'undefined' && object instanceof Uint8ClampedArray || object instanceof Int16Array || object instanceof Uint16Array || object instanceof Int32Array || object instanceof Uint32Array || object instanceof Float32Array || object instanceof Float64Array;
  	};

  	/**
    * Exactly what it says on the tin
    */
  	jsPDFAPI.binaryStringToUint8Array = function (binary_string) {
  		/*
     * not sure how efficient this will be will bigger files. Is there a native method?
     */
  		var len = binary_string.length;
  		var bytes = new Uint8Array(len);
  		for (var i = 0; i < len; i++) {
  			bytes[i] = binary_string.charCodeAt(i);
  		}
  		return bytes;
  	};

  	/**
    * @see this discussion
    * http://stackoverflow.com/questions/6965107/converting-between-strings-and-arraybuffers
    *
    * As stated, i imagine the method below is highly inefficent for large files.
    *
    * Also of note from Mozilla,
    *
    * "However, this is slow and error-prone, due to the need for multiple conversions (especially if the binary data is not actually byte-format data, but, for example, 32-bit integers or floats)."
    *
    * https://developer.mozilla.org/en-US/Add-ons/Code_snippets/StringView
    *
    * Although i'm strugglig to see how StringView solves this issue? Doesn't appear to be a direct method for conversion?
    *
    * Async method using Blob and FileReader could be best, but i'm not sure how to fit it into the flow?
    */
  	jsPDFAPI.arrayBufferToBinaryString = function (buffer) {
  		/*if('TextDecoder' in window){
    	var decoder = new TextDecoder('ascii');
    	return decoder.decode(buffer);
    }*/

  		if (this.isArrayBuffer(buffer)) buffer = new Uint8Array(buffer);

  		var binary_string = '';
  		var len = buffer.byteLength;
  		for (var i = 0; i < len; i++) {
  			binary_string += String.fromCharCode(buffer[i]);
  		}
  		return binary_string;
  		/*
     * Another solution is the method below - convert array buffer straight to base64 and then use atob
     */
  		//return atob(this.arrayBufferToBase64(buffer));
  	};

  	/**
    * Converts an ArrayBuffer directly to base64
    *
    * Taken from here
    *
    * http://jsperf.com/encoding-xhr-image-data/31
    *
    * Need to test if this is a better solution for larger files
    *
    */
  	jsPDFAPI.arrayBufferToBase64 = function (arrayBuffer) {
  		var base64 = '';
  		var encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  		var bytes = new Uint8Array(arrayBuffer);
  		var byteLength = bytes.byteLength;
  		var byteRemainder = byteLength % 3;
  		var mainLength = byteLength - byteRemainder;

  		var a, b, c, d;
  		var chunk;

  		// Main loop deals with bytes in chunks of 3
  		for (var i = 0; i < mainLength; i = i + 3) {
  			// Combine the three bytes into a single integer
  			chunk = bytes[i] << 16 | bytes[i + 1] << 8 | bytes[i + 2];

  			// Use bitmasks to extract 6-bit segments from the triplet
  			a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
  			b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
  			c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
  			d = chunk & 63; // 63       = 2^6 - 1

  			// Convert the raw binary segments to the appropriate ASCII encoding
  			base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
  		}

  		// Deal with the remaining bytes and padding
  		if (byteRemainder == 1) {
  			chunk = bytes[mainLength];

  			a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2

  			// Set the 4 least significant bits to zero
  			b = (chunk & 3) << 4; // 3   = 2^2 - 1

  			base64 += encodings[a] + encodings[b] + '==';
  		} else if (byteRemainder == 2) {
  			chunk = bytes[mainLength] << 8 | bytes[mainLength + 1];

  			a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
  			b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4

  			// Set the 2 least significant bits to zero
  			c = (chunk & 15) << 2; // 15    = 2^4 - 1

  			base64 += encodings[a] + encodings[b] + encodings[c] + '=';
  		}

  		return base64;
  	};

  	jsPDFAPI.createImageInfo = function (data, wd, ht, cs, bpc, f, imageIndex, alias, dp, trns, pal, smask, p) {
  		var info = {
  			alias: alias,
  			w: wd,
  			h: ht,
  			cs: cs,
  			bpc: bpc,
  			i: imageIndex,
  			data: data
  			// n: objectNumber will be added by putImage code
  		};

  		if (f) info.f = f;
  		if (dp) info.dp = dp;
  		if (trns) info.trns = trns;
  		if (pal) info.pal = pal;
  		if (smask) info.smask = smask;
  		if (p) info.p = p; // predictor parameter for PNG compression

  		return info;
  	};

  	jsPDFAPI.addImage = function (imageData, format, x, y, w, h, alias, compression, rotation) {
  		'use strict';

  		if (typeof format !== 'string') {
  			var tmp = h;
  			h = w;
  			w = y;
  			y = x;
  			x = format;
  			format = tmp;
  		}

  		if ((typeof imageData === 'undefined' ? 'undefined' : _typeof(imageData)) === 'object' && !isDOMElement(imageData) && "imageData" in imageData) {
  			var options = imageData;

  			imageData = options.imageData;
  			format = options.format || format;
  			x = options.x || x || 0;
  			y = options.y || y || 0;
  			w = options.w || w;
  			h = options.h || h;
  			alias = options.alias || alias;
  			compression = options.compression || compression;
  			rotation = options.rotation || options.angle || rotation;
  		}

  		if (isNaN(x) || isNaN(y)) {
  			console.error('jsPDF.addImage: Invalid coordinates', arguments);
  			throw new Error('Invalid coordinates passed to jsPDF.addImage');
  		}

  		var images = getImages.call(this),
  		    info;

  		if (!(info = checkImagesForAlias(imageData, images))) {
  			var dataAsBinaryString;

  			if (isDOMElement(imageData)) imageData = createDataURIFromElement(imageData, format, rotation);

  			if (notDefined(alias)) alias = generateAliasFromData(imageData);

  			if (!(info = checkImagesForAlias(alias, images))) {

  				if (this.isString(imageData)) {

  					var base64Info = this.extractInfoFromBase64DataURI(imageData);

  					if (base64Info) {

  						format = base64Info[2];
  						imageData = atob(base64Info[3]); //convert to binary string
  					} else {

  						if (imageData.charCodeAt(0) === 0x89 && imageData.charCodeAt(1) === 0x50 && imageData.charCodeAt(2) === 0x4e && imageData.charCodeAt(3) === 0x47) format = 'png';
  					}
  				}
  				format = (format || 'JPEG').toLowerCase();

  				if (doesNotSupportImageType(format)) throw new Error('addImage currently only supports formats ' + supported_image_types + ', not \'' + format + '\'');

  				if (processMethodNotEnabled(format)) throw new Error('please ensure that the plugin for \'' + format + '\' support is added');

  				/**
       * need to test if it's more efficient to convert all binary strings
       * to TypedArray - or should we just leave and process as string?
       */
  				if (this.supportsArrayBuffer()) {
  					// no need to convert if imageData is already uint8array
  					if (!(imageData instanceof Uint8Array)) {
  						dataAsBinaryString = imageData;
  						imageData = this.binaryStringToUint8Array(imageData);
  					}
  				}

  				info = this['process' + format.toUpperCase()](imageData, getImageIndex(images), alias, checkCompressValue(compression), dataAsBinaryString);

  				if (!info) throw new Error('An unkwown error occurred whilst processing the image');
  			}
  		}

  		writeImageToPDF.call(this, x, y, w, h, info, info.i, images);

  		return this;
  	};

  	/**
    * JPEG SUPPORT
    **/

  	//takes a string imgData containing the raw bytes of
  	//a jpeg image and returns [width, height]
  	//Algorithm from: http://www.64lines.com/jpeg-width-height
  	var getJpegSize = function getJpegSize(imgData) {
  		'use strict';

  		var width, height, numcomponents;
  		// Verify we have a valid jpeg header 0xff,0xd8,0xff,0xe0,?,?,'J','F','I','F',0x00
  		if (!imgData.charCodeAt(0) === 0xff || !imgData.charCodeAt(1) === 0xd8 || !imgData.charCodeAt(2) === 0xff || !imgData.charCodeAt(3) === 0xe0 || !imgData.charCodeAt(6) === 'J'.charCodeAt(0) || !imgData.charCodeAt(7) === 'F'.charCodeAt(0) || !imgData.charCodeAt(8) === 'I'.charCodeAt(0) || !imgData.charCodeAt(9) === 'F'.charCodeAt(0) || !imgData.charCodeAt(10) === 0x00) {
  			throw new Error('getJpegSize requires a binary string jpeg file');
  		}
  		var blockLength = imgData.charCodeAt(4) * 256 + imgData.charCodeAt(5);
  		var i = 4,
  		    len = imgData.length;
  		while (i < len) {
  			i += blockLength;
  			if (imgData.charCodeAt(i) !== 0xff) {
  				throw new Error('getJpegSize could not find the size of the image');
  			}
  			if (imgData.charCodeAt(i + 1) === 0xc0 || //(SOF) Huffman  - Baseline DCT
  			imgData.charCodeAt(i + 1) === 0xc1 || //(SOF) Huffman  - Extended sequential DCT
  			imgData.charCodeAt(i + 1) === 0xc2 || // Progressive DCT (SOF2)
  			imgData.charCodeAt(i + 1) === 0xc3 || // Spatial (sequential) lossless (SOF3)
  			imgData.charCodeAt(i + 1) === 0xc4 || // Differential sequential DCT (SOF5)
  			imgData.charCodeAt(i + 1) === 0xc5 || // Differential progressive DCT (SOF6)
  			imgData.charCodeAt(i + 1) === 0xc6 || // Differential spatial (SOF7)
  			imgData.charCodeAt(i + 1) === 0xc7) {
  				height = imgData.charCodeAt(i + 5) * 256 + imgData.charCodeAt(i + 6);
  				width = imgData.charCodeAt(i + 7) * 256 + imgData.charCodeAt(i + 8);
  				numcomponents = imgData.charCodeAt(i + 9);
  				return [width, height, numcomponents];
  			} else {
  				i += 2;
  				blockLength = imgData.charCodeAt(i) * 256 + imgData.charCodeAt(i + 1);
  			}
  		}
  	},
  	    getJpegSizeFromBytes = function getJpegSizeFromBytes(data) {

  		var hdr = data[0] << 8 | data[1];

  		if (hdr !== 0xFFD8) throw new Error('Supplied data is not a JPEG');

  		var len = data.length,
  		    block = (data[4] << 8) + data[5],
  		    pos = 4,
  		    bytes,
  		    width,
  		    height,
  		    numcomponents;

  		while (pos < len) {
  			pos += block;
  			bytes = readBytes(data, pos);
  			block = (bytes[2] << 8) + bytes[3];
  			if ((bytes[1] === 0xC0 || bytes[1] === 0xC2) && bytes[0] === 0xFF && block > 7) {
  				bytes = readBytes(data, pos + 5);
  				width = (bytes[2] << 8) + bytes[3];
  				height = (bytes[0] << 8) + bytes[1];
  				numcomponents = bytes[4];
  				return { width: width, height: height, numcomponents: numcomponents };
  			}

  			pos += 2;
  		}

  		throw new Error('getJpegSizeFromBytes could not find the size of the image');
  	},
  	    readBytes = function readBytes(data, offset) {
  		return data.subarray(offset, offset + 5);
  	};

  	jsPDFAPI.processJPEG = function (data, index, alias, compression, dataAsBinaryString) {
  		'use strict';

  		var colorSpace = this.color_spaces.DEVICE_RGB,
  		    filter = this.decode.DCT_DECODE,
  		    bpc = 8,
  		    dims;

  		if (this.isString(data)) {
  			dims = getJpegSize(data);
  			return this.createImageInfo(data, dims[0], dims[1], dims[3] == 1 ? this.color_spaces.DEVICE_GRAY : colorSpace, bpc, filter, index, alias);
  		}

  		if (this.isArrayBuffer(data)) data = new Uint8Array(data);

  		if (this.isArrayBufferView(data)) {

  			dims = getJpegSizeFromBytes(data);

  			// if we already have a stored binary string rep use that
  			data = dataAsBinaryString || this.arrayBufferToBinaryString(data);

  			return this.createImageInfo(data, dims.width, dims.height, dims.numcomponents == 1 ? this.color_spaces.DEVICE_GRAY : colorSpace, bpc, filter, index, alias);
  		}

  		return null;
  	};

  	jsPDFAPI.processJPG = function () /*data, index, alias, compression, dataAsBinaryString*/{
  		return this.processJPEG.apply(this, arguments);
  	};
  })(jsPDF.API);

  /**
   * jsPDF Annotations PlugIn
   * Copyright (c) 2014 Steven Spungin (TwelveTone LLC)  steven@twelvetone.tv
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  /**
   * There are many types of annotations in a PDF document. Annotations are placed
   * on a page at a particular location. They are not 'attached' to an object.
   * <br />
   * This plugin current supports <br />
   * <li> Goto Page (set pageNumber and top in options)
   * <li> Goto Name (set name and top in options)
   * <li> Goto URL (set url in options)
   * <p>
   * 	The destination magnification factor can also be specified when goto is a page number or a named destination. (see documentation below)
   *  (set magFactor in options).  XYZ is the default.
   * </p>
   * <p>
   *  Links, Text, Popup, and FreeText are supported.
   * </p>
   * <p>
   * Options In PDF spec Not Implemented Yet
   * <li> link border
   * <li> named target
   * <li> page coordinates
   * <li> destination page scaling and layout
   * <li> actions other than URL and GotoPage
   * <li> background / hover actions
   * </p>
   */

  /*
      Destination Magnification Factors
      See PDF 1.3 Page 386 for meanings and options

      [supported]
  	XYZ (options; left top zoom)
  	Fit (no options)
  	FitH (options: top)
  	FitV (options: left)

  	[not supported]
  	FitR
  	FitB
  	FitBH
  	FitBV
   */

  (function (jsPDFAPI) {
  	'use strict';

  	var annotationPlugin = {

  		/**
     * An array of arrays, indexed by <em>pageNumber</em>.
     */
  		annotations: [],

  		f2: function f2(number) {
  			return number.toFixed(2);
  		},

  		notEmpty: function notEmpty(obj) {
  			if (typeof obj != 'undefined') {
  				if (obj != '') {
  					return true;
  				}
  			}
  		}
  	};

  	jsPDF.API.annotationPlugin = annotationPlugin;

  	jsPDF.API.events.push(['addPage', function (info) {
  		this.annotationPlugin.annotations[info.pageNumber] = [];
  	}]);

  	jsPDFAPI.events.push(['putPage', function (info) {
  		//TODO store annotations in pageContext so reorder/remove will not affect them.
  		var pageAnnos = this.annotationPlugin.annotations[info.pageNumber];

  		var found = false;
  		for (var a = 0; a < pageAnnos.length && !found; a++) {
  			var anno = pageAnnos[a];
  			switch (anno.type) {
  				case 'link':
  					if (annotationPlugin.notEmpty(anno.options.url) || annotationPlugin.notEmpty(anno.options.pageNumber)) {
  						found = true;
  						break;
  					}
  				case 'reference':
  				case 'text':
  				case 'freetext':
  					found = true;
  					break;
  			}
  		}
  		if (found == false) {
  			return;
  		}

  		this.internal.write("/Annots [");
  		var f2 = this.annotationPlugin.f2;
  		var k = this.internal.scaleFactor;
  		var pageHeight = this.internal.pageSize.height;
  		var pageInfo = this.internal.getPageInfo(info.pageNumber);
  		for (var a = 0; a < pageAnnos.length; a++) {
  			var anno = pageAnnos[a];

  			switch (anno.type) {
  				case 'reference':
  					// References to Widget Anotations (for AcroForm Fields)
  					this.internal.write(' ' + anno.object.objId + ' 0 R ');
  					break;
  				case 'text':
  					// Create a an object for both the text and the popup
  					var objText = this.internal.newAdditionalObject();
  					var objPopup = this.internal.newAdditionalObject();

  					var title = anno.title || 'Note';
  					var rect = "/Rect [" + f2(anno.bounds.x * k) + " " + f2(pageHeight - (anno.bounds.y + anno.bounds.h) * k) + " " + f2((anno.bounds.x + anno.bounds.w) * k) + " " + f2((pageHeight - anno.bounds.y) * k) + "] ";
  					line = '<</Type /Annot /Subtype /' + 'Text' + ' ' + rect + '/Contents (' + anno.contents + ')';
  					line += ' /Popup ' + objPopup.objId + " 0 R";
  					line += ' /P ' + pageInfo.objId + " 0 R";
  					line += ' /T (' + title + ') >>';
  					objText.content = line;

  					var parent = objText.objId + ' 0 R';
  					var popoff = 30;
  					var rect = "/Rect [" + f2((anno.bounds.x + popoff) * k) + " " + f2(pageHeight - (anno.bounds.y + anno.bounds.h) * k) + " " + f2((anno.bounds.x + anno.bounds.w + popoff) * k) + " " + f2((pageHeight - anno.bounds.y) * k) + "] ";
  					//var rect2 = "/Rect [" + f2(anno.bounds.x * k) + " " + f2((pageHeight - anno.bounds.y) * k) + " " + f2(anno.bounds.x + anno.bounds.w * k) + " " + f2(pageHeight - (anno.bounds.y + anno.bounds.h) * k) + "] ";
  					line = '<</Type /Annot /Subtype /' + 'Popup' + ' ' + rect + ' /Parent ' + parent;
  					if (anno.open) {
  						line += ' /Open true';
  					}
  					line += ' >>';
  					objPopup.content = line;

  					this.internal.write(objText.objId, '0 R', objPopup.objId, '0 R');

  					break;
  				case 'freetext':
  					var rect = "/Rect [" + f2(anno.bounds.x * k) + " " + f2((pageHeight - anno.bounds.y) * k) + " " + f2(anno.bounds.x + anno.bounds.w * k) + " " + f2(pageHeight - (anno.bounds.y + anno.bounds.h) * k) + "] ";
  					var color = anno.color || '#000000';
  					line = '<</Type /Annot /Subtype /' + 'FreeText' + ' ' + rect + '/Contents (' + anno.contents + ')';
  					line += ' /DS(font: Helvetica,sans-serif 12.0pt; text-align:left; color:#' + color + ')';
  					line += ' /Border [0 0 0]';
  					line += ' >>';
  					this.internal.write(line);
  					break;
  				case 'link':
  					if (anno.options.name) {
  						var loc = this.annotations._nameMap[anno.options.name];
  						anno.options.pageNumber = loc.page;
  						anno.options.top = loc.y;
  					} else {
  						if (!anno.options.top) {
  							anno.options.top = 0;
  						}
  					}

  					//var pageHeight = this.internal.pageSize.height * this.internal.scaleFactor;
  					var rect = "/Rect [" + f2(anno.x * k) + " " + f2((pageHeight - anno.y) * k) + " " + f2(anno.x + anno.w * k) + " " + f2(pageHeight - (anno.y + anno.h) * k) + "] ";

  					var line = '';
  					if (anno.options.url) {
  						line = '<</Type /Annot /Subtype /Link ' + rect + '/Border [0 0 0] /A <</S /URI /URI (' + anno.options.url + ') >>';
  					} else if (anno.options.pageNumber) {
  						// first page is 0
  						var info = this.internal.getPageInfo(anno.options.pageNumber);
  						line = '<</Type /Annot /Subtype /Link ' + rect + '/Border [0 0 0] /Dest [' + info.objId + " 0 R";
  						anno.options.magFactor = anno.options.magFactor || "XYZ";
  						switch (anno.options.magFactor) {
  							case 'Fit':
  								line += ' /Fit]';
  								break;
  							case 'FitH':
  								//anno.options.top = anno.options.top || f2(pageHeight * k);
  								line += ' /FitH ' + anno.options.top + ']';
  								break;
  							case 'FitV':
  								anno.options.left = anno.options.left || 0;
  								line += ' /FitV ' + anno.options.left + ']';
  								break;
  							case 'XYZ':
  							default:
  								var top = f2((pageHeight - anno.options.top) * k); // || f2(pageHeight * k);
  								anno.options.left = anno.options.left || 0;
  								// 0 or null zoom will not change zoom factor
  								if (typeof anno.options.zoom === 'undefined') {
  									anno.options.zoom = 0;
  								}
  								line += ' /XYZ ' + anno.options.left + ' ' + top + ' ' + anno.options.zoom + ']';
  								break;
  						}
  					} else {
  						// TODO error - should not be here
  					}
  					if (line != '') {
  						line += " >>";
  						this.internal.write(line);
  					}
  					break;
  			}
  		}
  		this.internal.write("]");
  	}]);

  	jsPDFAPI.createAnnotation = function (options) {
  		switch (options.type) {
  			case 'link':
  				this.link(options.bounds.x, options.bounds.y, options.bounds.w, options.bounds.h, options);
  				break;
  			case 'text':
  			case 'freetext':
  				this.annotationPlugin.annotations[this.internal.getCurrentPageInfo().pageNumber].push(options);
  				break;
  		}
  	};

  	/**
    * valid options
    * <li> pageNumber or url [required]
    * <p>If pageNumber is specified, top and zoom may also be specified</p>
    */
  	jsPDFAPI.link = function (x, y, w, h, options) {
  		'use strict';

  		this.annotationPlugin.annotations[this.internal.getCurrentPageInfo().pageNumber].push({
  			x: x,
  			y: y,
  			w: w,
  			h: h,
  			options: options,
  			type: 'link'
  		});
  	};

  	/**
    * valid options
    * <li> pageNumber or url [required]
    * <p>If pageNumber is specified, top and zoom may also be specified</p>
    */
  	jsPDFAPI.link = function (x, y, w, h, options) {
  		'use strict';

  		this.annotationPlugin.annotations[this.internal.getCurrentPageInfo().pageNumber].push({
  			x: x,
  			y: y,
  			w: w,
  			h: h,
  			options: options,
  			type: 'link'
  		});
  	};

  	/**
    * Currently only supports single line text.
    * Returns the width of the text/link
    */
  	jsPDFAPI.textWithLink = function (text, x, y, options) {
  		'use strict';

  		var width = this.getTextWidth(text);
  		var height = this.internal.getLineHeight();
  		this.text(text, x, y);
  		//TODO We really need the text baseline height to do this correctly.
  		// Or ability to draw text on top, bottom, center, or baseline.
  		y += height * .2;
  		this.link(x, y - height, width, height, options);
  		return width;
  	};

  	//TODO move into external library
  	jsPDFAPI.getTextWidth = function (text) {
  		'use strict';

  		var fontSize = this.internal.getFontSize();
  		var txtWidth = this.getStringUnitWidth(text) * fontSize / this.internal.scaleFactor;
  		return txtWidth;
  	};

  	//TODO move into external library
  	jsPDFAPI.getLineHeight = function () {
  		return this.internal.getLineHeight();
  	};

  	return this;
  })(jsPDF.API);

  /**
   * jsPDF Autoprint Plugin
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  (function (jsPDFAPI) {
  	'use strict';

  	jsPDFAPI.autoPrint = function () {
  		'use strict';

  		var refAutoPrintTag;

  		this.internal.events.subscribe('postPutResources', function () {
  			refAutoPrintTag = this.internal.newObject();
  			this.internal.write("<< /S/Named /Type/Action /N/Print >>", "endobj");
  		});

  		this.internal.events.subscribe("putCatalog", function () {
  			this.internal.write("/OpenAction " + refAutoPrintTag + " 0" + " R");
  		});
  		return this;
  	};
  })(jsPDF.API);

  /**
   * jsPDF Canvas PlugIn
   * Copyright (c) 2014 Steven Spungin (TwelveTone LLC)  steven@twelvetone.tv
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  /**
   * This plugin mimics the HTML5 Canvas
   * 
   * The goal is to provide a way for current canvas users to print directly to a PDF.
   */

  (function (jsPDFAPI) {
  	'use strict';

  	jsPDFAPI.events.push(['initialized', function () {
  		this.canvas.pdf = this;
  	}]);

  	jsPDFAPI.canvas = {
  		getContext: function getContext(name) {
  			this.pdf.context2d._canvas = this;
  			return this.pdf.context2d;
  		},
  		style: {}
  	};

  	Object.defineProperty(jsPDFAPI.canvas, 'width', {
  		get: function get() {
  			return this._width;
  		},
  		set: function set(value) {
  			this._width = value;
  			this.getContext('2d').pageWrapX = value + 1;
  		}
  	});

  	Object.defineProperty(jsPDFAPI.canvas, 'height', {
  		get: function get() {
  			return this._height;
  		},
  		set: function set(value) {
  			this._height = value;
  			this.getContext('2d').pageWrapY = value + 1;
  		}
  	});

  	return this;
  })(jsPDF.API);

  /** ====================================================================
   * jsPDF Cell plugin
   * Copyright (c) 2013 Youssef Beddad, youssef.beddad@gmail.com
   *               2013 Eduardo Menezes de Morais, eduardo.morais@usp.br
   *               2013 Lee Driscoll, https://github.com/lsdriscoll
   *               2014 Juan Pablo Gaviria, https://github.com/juanpgaviria
   *               2014 James Hall, james@parall.ax
   *               2014 Diego Casorran, https://github.com/diegocr
   *
   * 
   * ====================================================================
   */

  (function (jsPDFAPI) {
      'use strict';
      /*jslint browser:true */
      /*global document: false, jsPDF */

      var fontName,
          fontSize,
          fontStyle,
          padding = 3,
          margin = 13,
          headerFunction,
          lastCellPos = { x: undefined, y: undefined, w: undefined, h: undefined, ln: undefined },
          pages = 1,
          setLastCellPosition = function setLastCellPosition(x, y, w, h, ln) {
          lastCellPos = { 'x': x, 'y': y, 'w': w, 'h': h, 'ln': ln };
      },
          getLastCellPosition = function getLastCellPosition() {
          return lastCellPos;
      },
          NO_MARGINS = { left: 0, top: 0, bottom: 0 };

      jsPDFAPI.setHeaderFunction = function (func) {
          headerFunction = func;
      };

      jsPDFAPI.getTextDimensions = function (txt) {
          fontName = this.internal.getFont().fontName;
          fontSize = this.table_font_size || this.internal.getFontSize();
          fontStyle = this.internal.getFont().fontStyle;
          // 1 pixel = 0.264583 mm and 1 mm = 72/25.4 point
          var px2pt = 0.264583 * 72 / 25.4,
              dimensions,
              text;

          text = document.createElement('font');
          text.id = "jsPDFCell";

          try {
              text.style.fontStyle = fontStyle;
          } catch (e) {
              text.style.fontWeight = fontStyle;
          }

          text.style.fontName = fontName;
          text.style.fontSize = fontSize + 'pt';
          try {
              text.textContent = txt;
          } catch (e) {
              text.innerText = txt;
          }

          document.body.appendChild(text);

          dimensions = { w: (text.offsetWidth + 1) * px2pt, h: (text.offsetHeight + 1) * px2pt };

          document.body.removeChild(text);

          return dimensions;
      };

      jsPDFAPI.cellAddPage = function () {
          var margins = this.margins || NO_MARGINS;

          this.addPage();

          setLastCellPosition(margins.left, margins.top, undefined, undefined);
          //setLastCellPosition(undefined, undefined, undefined, undefined, undefined);
          pages += 1;
      };

      jsPDFAPI.cellInitialize = function () {
          lastCellPos = { x: undefined, y: undefined, w: undefined, h: undefined, ln: undefined };
          pages = 1;
      };

      jsPDFAPI.cell = function (x, y, w, h, txt, ln, align) {
          var curCell = getLastCellPosition();
          var pgAdded = false;

          // If this is not the first cell, we must change its position
          if (curCell.ln !== undefined) {
              if (curCell.ln === ln) {
                  //Same line
                  x = curCell.x + curCell.w;
                  y = curCell.y;
              } else {
                  //New line
                  var margins = this.margins || NO_MARGINS;
                  if (curCell.y + curCell.h + h + margin >= this.internal.pageSize.height - margins.bottom) {
                      this.cellAddPage();
                      pgAdded = true;
                      if (this.printHeaders && this.tableHeaderRow) {
                          this.printHeaderRow(ln, true);
                      }
                  }
                  //We ignore the passed y: the lines may have different heights
                  y = getLastCellPosition().y + getLastCellPosition().h;
                  if (pgAdded) y = margin + 10;
              }
          }

          if (txt[0] !== undefined) {
              if (this.printingHeaderRow) {
                  this.rect(x, y, w, h, 'FD');
              } else {
                  this.rect(x, y, w, h);
              }
              if (align === 'right') {
                  if (!(txt instanceof Array)) {
                      txt = [txt];
                  }
                  for (var i = 0; i < txt.length; i++) {
                      var currentLine = txt[i];
                      var textSize = this.getStringUnitWidth(currentLine) * this.internal.getFontSize();
                      this.text(currentLine, x + w - textSize - padding, y + this.internal.getLineHeight() * (i + 1));
                  }
              } else {
                  this.text(txt, x + padding, y + this.internal.getLineHeight());
              }
          }
          setLastCellPosition(x, y, w, h, ln);
          return this;
      };

      /**
       * Return the maximum value from an array
       * @param array
       * @param comparisonFn
       * @returns {*}
       */
      jsPDFAPI.arrayMax = function (array, comparisonFn) {
          var max = array[0],
              i,
              ln,
              item;

          for (i = 0, ln = array.length; i < ln; i += 1) {
              item = array[i];

              if (comparisonFn) {
                  if (comparisonFn(max, item) === -1) {
                      max = item;
                  }
              } else {
                  if (item > max) {
                      max = item;
                  }
              }
          }

          return max;
      };

      /**
       * Create a table from a set of data.
       * @param {Integer} [x] : left-position for top-left corner of table
       * @param {Integer} [y] top-position for top-left corner of table
       * @param {Object[]} [data] As array of objects containing key-value pairs corresponding to a row of data.
       * @param {String[]} [headers] Omit or null to auto-generate headers at a performance cost
        * @param {Object} [config.printHeaders] True to print column headers at the top of every page
       * @param {Object} [config.autoSize] True to dynamically set the column widths to match the widest cell value
       * @param {Object} [config.margins] margin values for left, top, bottom, and width
       * @param {Object} [config.fontSize] Integer fontSize to use (optional)
       */

      jsPDFAPI.table = function (x, y, data, headers, config) {
          if (!data) {
              throw 'No data for PDF table';
          }

          var headerNames = [],
              headerPrompts = [],
              header,
              i,
              ln,
              cln,
              columnMatrix = {},
              columnWidths = {},
              columnData,
              column,
              columnMinWidths = [],
              j,
              tableHeaderConfigs = [],
              model,
              jln,
              func,


          //set up defaults. If a value is provided in config, defaults will be overwritten:
          autoSize = false,
              printHeaders = true,
              fontSize = 12,
              margins = NO_MARGINS;

          margins.width = this.internal.pageSize.width;

          if (config) {
              //override config defaults if the user has specified non-default behavior:
              if (config.autoSize === true) {
                  autoSize = true;
              }
              if (config.printHeaders === false) {
                  printHeaders = false;
              }
              if (config.fontSize) {
                  fontSize = config.fontSize;
              }
              if (config.css && typeof config.css['font-size'] !== "undefined") {
                  fontSize = config.css['font-size'] * 16;
              }
              if (config.margins) {
                  margins = config.margins;
              }
          }

          /**
           * @property {Number} lnMod
           * Keep track of the current line number modifier used when creating cells
           */
          this.lnMod = 0;
          lastCellPos = { x: undefined, y: undefined, w: undefined, h: undefined, ln: undefined }, pages = 1;

          this.printHeaders = printHeaders;
          this.margins = margins;
          this.setFontSize(fontSize);
          this.table_font_size = fontSize;

          // Set header values
          if (headers === undefined || headers === null) {
              // No headers defined so we derive from data
              headerNames = Object.keys(data[0]);
          } else if (headers[0] && typeof headers[0] !== 'string') {
              var px2pt = 0.264583 * 72 / 25.4;

              // Split header configs into names and prompts
              for (i = 0, ln = headers.length; i < ln; i += 1) {
                  header = headers[i];
                  headerNames.push(header.name);
                  headerPrompts.push(header.prompt);
                  columnWidths[header.name] = header.width * px2pt;
              }
          } else {
              headerNames = headers;
          }

          if (autoSize) {
              // Create a matrix of columns e.g., {column_title: [row1_Record, row2_Record]}
              func = function func(rec) {
                  return rec[header];
              };

              for (i = 0, ln = headerNames.length; i < ln; i += 1) {
                  header = headerNames[i];

                  columnMatrix[header] = data.map(func);

                  // get header width
                  columnMinWidths.push(this.getTextDimensions(headerPrompts[i] || header).w);
                  column = columnMatrix[header];

                  // get cell widths
                  for (j = 0, cln = column.length; j < cln; j += 1) {
                      columnData = column[j];
                      columnMinWidths.push(this.getTextDimensions(columnData).w);
                  }

                  // get final column width
                  columnWidths[header] = jsPDFAPI.arrayMax(columnMinWidths);

                  //have to reset
                  columnMinWidths = [];
              }
          }

          // -- Construct the table

          if (printHeaders) {
              var lineHeight = this.calculateLineHeight(headerNames, columnWidths, headerPrompts.length ? headerPrompts : headerNames);

              // Construct the header row
              for (i = 0, ln = headerNames.length; i < ln; i += 1) {
                  header = headerNames[i];
                  tableHeaderConfigs.push([x, y, columnWidths[header], lineHeight, String(headerPrompts.length ? headerPrompts[i] : header)]);
              }

              // Store the table header config
              this.setTableHeaderRow(tableHeaderConfigs);

              // Print the header for the start of the table
              this.printHeaderRow(1, false);
          }

          // Construct the data rows
          for (i = 0, ln = data.length; i < ln; i += 1) {
              var lineHeight;
              model = data[i];
              lineHeight = this.calculateLineHeight(headerNames, columnWidths, model);

              for (j = 0, jln = headerNames.length; j < jln; j += 1) {
                  header = headerNames[j];
                  this.cell(x, y, columnWidths[header], lineHeight, model[header], i + 2, header.align);
              }
          }
          this.lastCellPos = lastCellPos;
          this.table_x = x;
          this.table_y = y;
          return this;
      };
      /**
       * Calculate the height for containing the highest column
       * @param {String[]} headerNames is the header, used as keys to the data
       * @param {Integer[]} columnWidths is size of each column
       * @param {Object[]} model is the line of data we want to calculate the height of
       */
      jsPDFAPI.calculateLineHeight = function (headerNames, columnWidths, model) {
          var header,
              lineHeight = 0;
          for (var j = 0; j < headerNames.length; j++) {
              header = headerNames[j];
              model[header] = this.splitTextToSize(String(model[header]), columnWidths[header] - padding);
              var h = this.internal.getLineHeight() * model[header].length + padding;
              if (h > lineHeight) lineHeight = h;
          }
          return lineHeight;
      };

      /**
       * Store the config for outputting a table header
       * @param {Object[]} config
       * An array of cell configs that would define a header row: Each config matches the config used by jsPDFAPI.cell
       * except the ln parameter is excluded
       */
      jsPDFAPI.setTableHeaderRow = function (config) {
          this.tableHeaderRow = config;
      };

      /**
       * Output the store header row
       * @param lineNumber The line number to output the header at
       */
      jsPDFAPI.printHeaderRow = function (lineNumber, new_page) {
          if (!this.tableHeaderRow) {
              throw 'Property tableHeaderRow does not exist.';
          }

          var tableHeaderCell, tmpArray, i, ln;

          this.printingHeaderRow = true;
          if (headerFunction !== undefined) {
              var position = headerFunction(this, pages);
              setLastCellPosition(position[0], position[1], position[2], position[3], -1);
          }
          this.setFontStyle('bold');
          var tempHeaderConf = [];
          for (i = 0, ln = this.tableHeaderRow.length; i < ln; i += 1) {
              this.setFillColor(200, 200, 200);

              tableHeaderCell = this.tableHeaderRow[i];
              if (new_page) {
                  this.margins.top = margin;
                  tableHeaderCell[1] = this.margins && this.margins.top || 0;
                  tempHeaderConf.push(tableHeaderCell);
              }
              tmpArray = [].concat(tableHeaderCell);
              this.cell.apply(this, tmpArray.concat(lineNumber));
          }
          if (tempHeaderConf.length > 0) {
              this.setTableHeaderRow(tempHeaderConf);
          }
          this.setFontStyle('normal');
          this.printingHeaderRow = false;
      };
  })(jsPDF.API);

  /**
   * jsPDF Context2D PlugIn Copyright (c) 2014 Steven Spungin (TwelveTone LLC) steven@twelvetone.tv
   *
   * Licensed under the MIT License. http://opensource.org/licenses/mit-license
   */

  /**
   * This plugin mimics the HTML5 Canvas's context2d.
   *
   * The goal is to provide a way for current canvas implementations to print directly to a PDF.
   */

  /**
   * TODO implement stroke opacity (refactor from fill() method )
   * TODO transform angle and radii parameters
   */

  /**
   * require('jspdf.js'); require('lib/css_colors.js');
   */

  (function (jsPDFAPI) {
      'use strict';

      jsPDFAPI.events.push(['initialized', function () {
          this.context2d.pdf = this;
          this.context2d.internal.pdf = this;
          this.context2d.ctx = new context();
          this.context2d.ctxStack = [];
          this.context2d.path = [];
      }]);

      jsPDFAPI.context2d = {
          pageWrapXEnabled: false,
          pageWrapYEnabled: false,
          pageWrapX: 9999999,
          pageWrapY: 9999999,
          ctx: new context(),
          f2: function f2(number) {
              return number.toFixed(2);
          },

          fillRect: function fillRect(x, y, w, h) {
              if (this._isFillTransparent()) {
                  return;
              }
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xRect = this._matrix_map_rect(this.ctx._transform, { x: x, y: y, w: w, h: h });
              this.pdf.rect(xRect.x, xRect.y, xRect.w, xRect.h, "f");
          },

          strokeRect: function strokeRect(x, y, w, h) {
              if (this._isStrokeTransparent()) {
                  return;
              }
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xRect = this._matrix_map_rect(this.ctx._transform, { x: x, y: y, w: w, h: h });
              this.pdf.rect(xRect.x, xRect.y, xRect.w, xRect.h, "s");
          },

          clearRect: function clearRect(x, y, w, h) {
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xRect = this._matrix_map_rect(this.ctx._transform, { x: x, y: y, w: w, h: h });
              this.save();
              this.setFillStyle('#ffffff');
              //TODO This is hack to fill with white.
              this.pdf.rect(xRect.x, xRect.y, xRect.w, xRect.h, "f");
              this.restore();
          },

          save: function save() {
              this.ctx._fontSize = this.pdf.internal.getFontSize();
              var ctx = new context();
              ctx.copy(this.ctx);
              this.ctxStack.push(this.ctx);
              this.ctx = ctx;
          },

          restore: function restore() {
              this.ctx = this.ctxStack.pop();
              this.setFillStyle(this.ctx.fillStyle);
              this.setStrokeStyle(this.ctx.strokeStyle);
              this.setFont(this.ctx.font);
              this.pdf.setFontSize(this.ctx._fontSize);
              this.setLineCap(this.ctx.lineCap);
              this.setLineWidth(this.ctx.lineWidth);
              this.setLineJoin(this.ctx.lineJoin);
          },

          rect: function rect(x, y, w, h) {
              this.moveTo(x, y);
              this.lineTo(x + w, y);
              this.lineTo(x + w, y + h);
              this.lineTo(x, y + h);
              this.lineTo(x, y); //TODO not needed
              this.closePath();
          },

          beginPath: function beginPath() {
              this.path = [];
          },

          closePath: function closePath() {
              this.path.push({
                  type: 'close'
              });
          },

          _getRgba: function _getRgba(style) {
              // get the decimal values of r, g, and b;
              var rgba = {};

              if (this.internal.rxTransparent.test(style)) {
                  rgba.r = 0;
                  rgba.g = 0;
                  rgba.b = 0;
                  rgba.a = 0;
              } else {
                  var m = this.internal.rxRgb.exec(style);
                  if (m != null) {
                      rgba.r = parseInt(m[1]);
                      rgba.g = parseInt(m[2]);
                      rgba.b = parseInt(m[3]);
                      rgba.a = 1;
                  } else {
                      m = this.internal.rxRgba.exec(style);
                      if (m != null) {
                          rgba.r = parseInt(m[1]);
                          rgba.g = parseInt(m[2]);
                          rgba.b = parseInt(m[3]);
                          rgba.a = parseFloat(m[4]);
                      } else {
                          rgba.a = 1;
                          if (style.charAt(0) != '#') {
                              style = CssColors.colorNameToHex(style);
                              if (!style) {
                                  style = '#000000';
                              }
                          } else {}

                          if (style.length === 4) {
                              rgba.r = style.substring(1, 2);
                              rgba.r += r;
                              rgba.g = style.substring(2, 3);
                              rgba.g += g;
                              rgba.b = style.substring(3, 4);
                              rgba.b += b;
                          } else {
                              rgba.r = style.substring(1, 3);
                              rgba.g = style.substring(3, 5);
                              rgba.b = style.substring(5, 7);
                          }
                          rgba.r = parseInt(rgba.r, 16);
                          rgba.g = parseInt(rgba.g, 16);
                          rgba.b = parseInt(rgba.b, 16);
                      }
                  }
              }
              rgba.style = style;
              return rgba;
          },

          setFillStyle: function setFillStyle(style) {
              // get the decimal values of r, g, and b;
              var r, g, b, a;

              if (this.internal.rxTransparent.test(style)) {
                  r = 0;
                  g = 0;
                  b = 0;
                  a = 0;
              } else {
                  var m = this.internal.rxRgb.exec(style);
                  if (m != null) {
                      r = parseInt(m[1]);
                      g = parseInt(m[2]);
                      b = parseInt(m[3]);
                      a = 1;
                  } else {
                      m = this.internal.rxRgba.exec(style);
                      if (m != null) {
                          r = parseInt(m[1]);
                          g = parseInt(m[2]);
                          b = parseInt(m[3]);
                          a = parseFloat(m[4]);
                      } else {
                          a = 1;
                          if (style.charAt(0) != '#') {
                              style = CssColors.colorNameToHex(style);
                              if (!style) {
                                  style = '#000000';
                              }
                          } else {}

                          if (style.length === 4) {
                              r = style.substring(1, 2);
                              r += r;
                              g = style.substring(2, 3);
                              g += g;
                              b = style.substring(3, 4);
                              b += b;
                          } else {
                              r = style.substring(1, 3);
                              g = style.substring(3, 5);
                              b = style.substring(5, 7);
                          }
                          r = parseInt(r, 16);
                          g = parseInt(g, 16);
                          b = parseInt(b, 16);
                      }
                  }
              }

              this.ctx.fillStyle = style;
              this.ctx._isFillTransparent = a == 0;
              this.ctx._fillOpacity = a;

              this.pdf.setFillColor(r, g, b, {
                  a: a
              });
              this.pdf.setTextColor(r, g, b, {
                  a: a
              });
          },

          setStrokeStyle: function setStrokeStyle(style) {
              var rgba = this._getRgba(style);

              this.ctx.strokeStyle = rgba.style;
              this.ctx._isStrokeTransparent = rgba.a == 0;
              this.ctx._strokeOpacity = rgba.a;

              //TODO jsPDF to handle rgba
              if (rgba.a === 0) {
                  this.pdf.setDrawColor(255, 255, 255);
              } else if (rgba.a === 1) {
                  this.pdf.setDrawColor(rgba.r, rgba.g, rgba.b);
              } else {
                  //this.pdf.setDrawColor(rgba.r, rgba.g, rgba.b, {a: rgba.a});
                  this.pdf.setDrawColor(rgba.r, rgba.g, rgba.b);
              }
          },

          fillText: function fillText(text, x, y, maxWidth) {
              if (this._isFillTransparent()) {
                  return;
              }
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];
              var rads = this._matrix_rotation(this.ctx._transform);
              var degs = rads * 57.2958;

              //TODO only push the clip if it has not been applied to the current PDF context
              if (this.ctx._clip_path.length > 0) {
                  var lines;
                  if (window.outIntercept) {
                      lines = window.outIntercept.type === 'group' ? window.outIntercept.stream : window.outIntercept;
                  } else {
                      lines = this.pdf.internal.pages[1];
                  }
                  lines.push("q");
                  var origPath = this.path;
                  this.path = this.ctx._clip_path;
                  this.ctx._clip_path = [];
                  this._fill(null, true);
                  this.ctx._clip_path = this.path;
                  this.path = origPath;
              }

              this.pdf.text(text, x, this._getBaseline(y), null, degs);

              if (this.ctx._clip_path.length > 0) {
                  lines.push('Q');
              }
          },

          strokeText: function strokeText(text, x, y, maxWidth) {
              if (this._isStrokeTransparent()) {
                  return;
              }
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];
              var rads = this._matrix_rotation(this.ctx._transform);
              var degs = rads * 57.2958;

              //TODO only push the clip if it has not been applied to the current PDF context
              if (this.ctx._clip_path.length > 0) {
                  var lines;
                  if (window.outIntercept) {
                      lines = window.outIntercept.type === 'group' ? window.outIntercept.stream : window.outIntercept;
                  } else {
                      lines = this.pdf.internal.pages[1];
                  }
                  lines.push("q");
                  var origPath = this.path;
                  this.path = this.ctx._clip_path;
                  this.ctx._clip_path = [];
                  this._fill(null, true);
                  this.ctx._clip_path = this.path;
                  this.path = origPath;
              }

              this.pdf.text(text, x, this._getBaseline(y), {
                  stroke: true
              }, degs);

              if (this.ctx._clip_path.length > 0) {
                  lines.push('Q');
              }
          },

          setFont: function setFont(font) {
              this.ctx.font = font;

              //var rx = /\s*(\w+)\s+(\w+)\s+(\w+)\s+([\d\.]+)(px|pt|em)\s+["']?(\w+)['"]?/;
              var rx = /\s*(\w+)\s+(\w+)\s+(\w+)\s+([\d\.]+)(px|pt|em)\s+(.*)?/;
              m = rx.exec(font);
              if (m != null) {
                  var fontStyle = m[1];
                  var fontVariant = m[2];
                  var fontWeight = m[3];
                  var fontSize = m[4];
                  var fontSizeUnit = m[5];
                  var fontFamily = m[6];

                  if ('px' === fontSizeUnit) {
                      fontSize = Math.floor(parseFloat(fontSize));
                      // fontSize = fontSize * 1.25;
                  } else if ('em' === fontSizeUnit) {
                      fontSize = Math.floor(parseFloat(fontSize) * this.pdf.getFontSize());
                  } else {
                      fontSize = Math.floor(parseFloat(fontSize));
                  }

                  this.pdf.setFontSize(fontSize);

                  if (fontWeight === 'bold' || fontWeight === '700') {
                      this.pdf.setFontStyle('bold');
                  } else {
                      if (fontStyle === 'italic') {
                          this.pdf.setFontStyle('italic');
                      } else {
                          this.pdf.setFontStyle('normal');
                      }
                  }

                  var name = fontFamily;
                  var parts = name.toLowerCase().split(/\s*,\s*/);
                  var jsPdfFontName;

                  if (parts.indexOf('arial') != -1) {
                      jsPdfFontName = 'Arial';
                  } else if (parts.indexOf('verdana') != -1) {
                      jsPdfFontName = 'Verdana';
                  } else if (parts.indexOf('helvetica') != -1) {
                      jsPdfFontName = 'Helvetica';
                  } else if (parts.indexOf('sans-serif') != -1) {
                      jsPdfFontName = 'sans-serif';
                  } else if (parts.indexOf('fixed') != -1) {
                      jsPdfFontName = 'Fixed';
                  } else if (parts.indexOf('monospace') != -1) {
                      jsPdfFontName = 'Monospace';
                  } else if (parts.indexOf('terminal') != -1) {
                      jsPdfFontName = 'Terminal';
                  } else if (parts.indexOf('courier') != -1) {
                      jsPdfFontName = 'Courier';
                  } else if (parts.indexOf('times') != -1) {
                      jsPdfFontName = 'Times';
                  } else if (parts.indexOf('cursive') != -1) {
                      jsPdfFontName = 'Cursive';
                  } else if (parts.indexOf('fantasy') != -1) {
                      jsPdfFontName = 'Fantasy';
                  } else if (parts.indexOf('serif') != -1) {
                      jsPdfFontName = 'Serif';
                  } else {
                      jsPdfFontName = 'Serif';
                  }

                  //TODO check more cases
                  var style;
                  if ('bold' === fontWeight) {
                      style = 'bold';
                  } else {
                      style = 'normal';
                  }

                  this.pdf.setFont(jsPdfFontName, style);
              } else {
                  var rx = /(\d+)(pt|px|em)\s+(\w+)\s*(\w+)?/;
                  var m = rx.exec(font);
                  if (m != null) {
                      var size = m[1];
                      var unit = m[2];
                      var name = m[3];
                      var style = m[4];
                      if (!style) {
                          style = 'normal';
                      }
                      if ('em' === fontSizeUnit) {
                          size = Math.floor(parseFloat(fontSize) * this.pdf.getFontSize());
                      } else {
                          size = Math.floor(parseFloat(size));
                      }
                      this.pdf.setFontSize(size);
                      this.pdf.setFont(name, style);
                  }
              }
          },

          setTextBaseline: function setTextBaseline(baseline) {
              this.ctx.textBaseline = baseline;
          },

          getTextBaseline: function getTextBaseline() {
              return this.ctx.textBaseline;
          },

          //TODO implement textAlign
          setTextAlign: function setTextAlign(align) {
              this.ctx.textAlign = align;
          },

          getTextAlign: function getTextAlign() {
              return this.ctx.textAlign;
          },

          setLineWidth: function setLineWidth(width) {
              this.ctx.lineWidth = width;
              this.pdf.setLineWidth(width);
          },

          setLineCap: function setLineCap(style) {
              this.ctx.lineCap = style;
              this.pdf.setLineCap(style);
          },

          setLineJoin: function setLineJoin(style) {
              this.ctx.lineJoin = style;
              this.pdf.setLineJoin(style);
          },

          moveTo: function moveTo(x, y) {
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];

              var obj = {
                  type: 'mt',
                  x: x,
                  y: y
              };
              this.path.push(obj);
          },

          _wrapX: function _wrapX(x) {
              if (this.pageWrapXEnabled) {
                  return x % this.pageWrapX;
              } else {
                  return x;
              }
          },

          _wrapY: function _wrapY(y) {
              if (this.pageWrapYEnabled) {
                  this._gotoPage(this._page(y));
                  return (y - this.lastBreak) % this.pageWrapY;
              } else {
                  return y;
              }
          },

          transform: function transform(a, b, c, d, e, f) {
              //TODO apply to current transformation instead of replacing
              this.ctx._transform = [a, b, c, d, e, f];
          },

          setTransform: function setTransform(a, b, c, d, e, f) {
              this.ctx._transform = [a, b, c, d, e, f];
          },

          _getTransform: function _getTransform() {
              return this.ctx._transform;
          },

          lastBreak: 0,
          // Y Position of page breaks.
          pageBreaks: [],
          // returns: One-based Page Number
          // Should only be used if pageWrapYEnabled is true
          _page: function _page(y) {
              if (this.pageWrapYEnabled) {
                  this.lastBreak = 0;
                  var manualBreaks = 0;
                  var autoBreaks = 0;
                  for (var i = 0; i < this.pageBreaks.length; i++) {
                      if (y >= this.pageBreaks[i]) {
                          manualBreaks++;
                          if (this.lastBreak === 0) {
                              autoBreaks++;
                          }
                          var spaceBetweenLastBreak = this.pageBreaks[i] - this.lastBreak;
                          this.lastBreak = this.pageBreaks[i];
                          var pagesSinceLastBreak = Math.floor(spaceBetweenLastBreak / this.pageWrapY);
                          autoBreaks += pagesSinceLastBreak;
                      }
                  }
                  if (this.lastBreak === 0) {
                      var pagesSinceLastBreak = Math.floor(y / this.pageWrapY) + 1;
                      autoBreaks += pagesSinceLastBreak;
                  }
                  return autoBreaks + manualBreaks;
              } else {
                  return this.pdf.internal.getCurrentPageInfo().pageNumber;
              }
          },

          _gotoPage: function _gotoPage(pageOneBased) {
              // This is a stub to be overriden if needed
          },

          lineTo: function lineTo(x, y) {
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];

              var obj = {
                  type: 'lt',
                  x: x,
                  y: y
              };
              this.path.push(obj);
          },

          bezierCurveTo: function bezierCurveTo(x1, y1, x2, y2, x, y) {
              x1 = this._wrapX(x1);
              y1 = this._wrapY(y1);
              x2 = this._wrapX(x2);
              y2 = this._wrapY(y2);
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt;
              xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];
              xpt = this._matrix_map_point(this.ctx._transform, [x1, y1]);
              x1 = xpt[0];
              y1 = xpt[1];
              xpt = this._matrix_map_point(this.ctx._transform, [x2, y2]);
              x2 = xpt[0];
              y2 = xpt[1];

              var obj = {
                  type: 'bct',
                  x1: x1,
                  y1: y1,
                  x2: x2,
                  y2: y2,
                  x: x,
                  y: y
              };
              this.path.push(obj);
          },

          quadraticCurveTo: function quadraticCurveTo(x1, y1, x, y) {
              x1 = this._wrapX(x1);
              y1 = this._wrapY(y1);
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt;
              xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];
              xpt = this._matrix_map_point(this.ctx._transform, [x1, y1]);
              x1 = xpt[0];
              y1 = xpt[1];

              var obj = {
                  type: 'qct',
                  x1: x1,
                  y1: y1,
                  x: x,
                  y: y
              };
              this.path.push(obj);
          },

          arc: function arc(x, y, radius, startAngle, endAngle, anticlockwise) {
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];

              var obj = {
                  type: 'arc',
                  x: x,
                  y: y,
                  radius: radius,
                  startAngle: startAngle,
                  endAngle: endAngle,
                  anticlockwise: anticlockwise
              };
              this.path.push(obj);
          },

          drawImage: function drawImage(img, x, y, w, h, x2, y2, w2, h2) {
              if (x2 !== undefined) {
                  x = x2;
                  y = y2;
                  w = w2;
                  h = h2;
              }
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xRect = this._matrix_map_rect(this.ctx._transform, { x: x, y: y, w: w, h: h });
              var xRect2 = this._matrix_map_rect(this.ctx._transform, { x: x2, y: y2, w: w2, h: h2 });

              // TODO implement source clipping and image scaling
              var format;
              var rx = /data:image\/(\w+).*/i;
              var m = rx.exec(img);
              if (m != null) {
                  format = m[1];
              } else {
                  // format = "jpeg";
                  format = "png";
              }

              this.pdf.addImage(img, format, xRect.x, xRect.y, xRect.w, xRect.h);
          },

          /**
           * Multiply the first matrix by the second
           * @param m1
           * @param m2
           * @returns {*[]}
           * @private
           */
          _matrix_multiply: function _matrix_multiply(m2, m1) {
              var sx = m1[0];
              var shy = m1[1];
              var shx = m1[2];
              var sy = m1[3];
              var tx = m1[4];
              var ty = m1[5];

              var t0 = sx * m2[0] + shy * m2[2];
              var t2 = shx * m2[0] + sy * m2[2];
              var t4 = tx * m2[0] + ty * m2[2] + m2[4];
              shy = sx * m2[1] + shy * m2[3];
              sy = shx * m2[1] + sy * m2[3];
              ty = tx * m2[1] + ty * m2[3] + m2[5];
              sx = t0;
              shx = t2;
              tx = t4;

              return [sx, shy, shx, sy, tx, ty];
          },

          _matrix_rotation: function _matrix_rotation(m) {
              return Math.atan2(m[2], m[0]);
          },

          _matrix_decompose: function _matrix_decompose(matrix) {

              var a = matrix[0];
              var b = matrix[1];
              var c = matrix[2];
              var d = matrix[3];

              var scaleX = Math.sqrt(a * a + b * b);
              a /= scaleX;
              b /= scaleX;

              var shear = a * c + b * d;
              c -= a * shear;
              d -= b * shear;

              var scaleY = Math.sqrt(c * c + d * d);
              c /= scaleY;
              d /= scaleY;
              shear /= scaleY;

              if (a * d < b * c) {
                  a = -a;
                  b = -b;
                  shear = -shear;
                  scaleX = -scaleX;
              }

              return {
                  scale: [scaleX, 0, 0, scaleY, 0, 0],
                  translate: [1, 0, 0, 1, matrix[4], matrix[5]],
                  rotate: [a, b, -b, a, 0, 0],
                  skew: [1, 0, shear, 1, 0, 0]
              };
          },

          _matrix_map_point: function _matrix_map_point(m1, pt) {
              var sx = m1[0];
              var shy = m1[1];
              var shx = m1[2];
              var sy = m1[3];
              var tx = m1[4];
              var ty = m1[5];

              var px = pt[0];
              var py = pt[1];

              var x = px * sx + py * shx + tx;
              var y = px * shy + py * sy + ty;
              return [x, y];
          },

          _matrix_map_point_obj: function _matrix_map_point_obj(m1, pt) {
              var xpt = this._matrix_map_point(m1, [pt.x, pt.y]);
              return { x: xpt[0], y: xpt[1] };
          },

          _matrix_map_rect: function _matrix_map_rect(m1, rect) {
              var p1 = this._matrix_map_point(m1, [rect.x, rect.y]);
              var p2 = this._matrix_map_point(m1, [rect.x + rect.w, rect.y + rect.h]);
              return { x: p1[0], y: p1[1], w: p2[0] - p1[0], h: p2[1] - p1[1] };
          },

          _matrix_is_identity: function _matrix_is_identity(m1) {
              if (m1[0] != 1) {
                  return false;
              }
              if (m1[1] != 0) {
                  return false;
              }
              if (m1[2] != 0) {
                  return false;
              }
              if (m1[3] != 1) {
                  return false;
              }
              if (m1[4] != 0) {
                  return false;
              }
              if (m1[5] != 0) {
                  return false;
              }
              return true;
          },

          rotate: function rotate(angle) {
              var matrix = [Math.cos(angle), Math.sin(angle), -Math.sin(angle), Math.cos(angle), 0.0, 0.0];
              this.ctx._transform = this._matrix_multiply(this.ctx._transform, matrix);
          },

          scale: function scale(sx, sy) {
              var matrix = [sx, 0.0, 0.0, sy, 0.0, 0.0];
              this.ctx._transform = this._matrix_multiply(this.ctx._transform, matrix);
          },

          translate: function translate(x, y) {
              var matrix = [1.0, 0.0, 0.0, 1.0, x, y];
              this.ctx._transform = this._matrix_multiply(this.ctx._transform, matrix);
          },

          stroke: function stroke() {
              if (this.ctx._clip_path.length > 0) {

                  var lines;
                  if (window.outIntercept) {
                      lines = window.outIntercept.type === 'group' ? window.outIntercept.stream : window.outIntercept;
                  } else {
                      lines = this.pdf.internal.pages[1];
                  }
                  lines.push("q");

                  var origPath = this.path;
                  this.path = this.ctx._clip_path;
                  this.ctx._clip_path = [];
                  this._stroke(true);

                  this.ctx._clip_path = this.path;
                  this.path = origPath;
                  this._stroke(false);

                  lines.push("Q");
              } else {
                  this._stroke(false);
              }
          },

          _stroke: function _stroke(isClip) {
              if (!isClip && this._isStrokeTransparent()) {
                  return;
              }

              //TODO opacity

              var moves = [];
              var closed = false;

              var xPath = this.path;

              for (var i = 0; i < xPath.length; i++) {
                  var pt = xPath[i];
                  switch (pt.type) {
                      case 'mt':
                          moves.push({ start: pt, deltas: [], abs: [] });
                          break;
                      case 'lt':
                          var delta = [pt.x - xPath[i - 1].x, pt.y - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          moves[moves.length - 1].abs.push(pt);
                          break;
                      case 'bct':
                          var delta = [pt.x1 - xPath[i - 1].x, pt.y1 - xPath[i - 1].y, pt.x2 - xPath[i - 1].x, pt.y2 - xPath[i - 1].y, pt.x - xPath[i - 1].x, pt.y - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          break;
                      case 'qct':
                          // convert to bezier
                          var x1 = xPath[i - 1].x + 2.0 / 3.0 * (pt.x1 - xPath[i - 1].x);
                          var y1 = xPath[i - 1].y + 2.0 / 3.0 * (pt.y1 - xPath[i - 1].y);
                          var x2 = pt.x + 2.0 / 3.0 * (pt.x1 - pt.x);
                          var y2 = pt.y + 2.0 / 3.0 * (pt.y1 - pt.y);
                          var x3 = pt.x;
                          var y3 = pt.y;
                          var delta = [x1 - xPath[i - 1].x, y1 - xPath[i - 1].y, x2 - xPath[i - 1].x, y2 - xPath[i - 1].y, x3 - xPath[i - 1].x, y3 - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          break;
                      case 'arc':
                          moves[moves.length - 1].arc = true;
                          moves[moves.length - 1].abs.push(pt);
                          break;
                      case 'close':
                          closed = true;
                          break;
                  }
              }

              for (var i = 0; i < moves.length; i++) {
                  var style;
                  if (i == moves.length - 1) {
                      style = 's';
                  } else {
                      style = null;
                  }
                  if (moves[i].arc) {
                      var arcs = moves[i].abs;
                      for (var ii = 0; ii < arcs.length; ii++) {
                          var arc = arcs[ii];
                          var start = arc.startAngle * 360 / (2 * Math.PI);
                          var end = arc.endAngle * 360 / (2 * Math.PI);
                          var x = arc.x;
                          var y = arc.y;
                          this.internal.arc2(this, x, y, arc.radius, start, end, arc.anticlockwise, style, isClip);
                      }
                  } else {
                      var x = moves[i].start.x;
                      var y = moves[i].start.y;
                      if (!isClip) {
                          this.pdf.lines(moves[i].deltas, x, y, null, style);
                      } else {
                          this.pdf.lines(moves[i].deltas, x, y, null, null);
                          this.pdf.clip_fixed();
                      }
                  }
              }
          },

          _isFillTransparent: function _isFillTransparent() {
              return this.ctx._isFillTransparent || this.globalAlpha == 0;
          },

          _isStrokeTransparent: function _isStrokeTransparent() {
              return this.ctx._isStrokeTransparent || this.globalAlpha == 0;
          },

          fill: function fill(fillRule) {
              //evenodd or nonzero (default)
              if (this.ctx._clip_path.length > 0) {

                  var lines;
                  if (window.outIntercept) {
                      lines = window.outIntercept.type === 'group' ? window.outIntercept.stream : window.outIntercept;
                  } else {
                      lines = this.pdf.internal.pages[1];
                  }
                  lines.push("q");

                  var origPath = this.path;
                  this.path = this.ctx._clip_path;
                  this.ctx._clip_path = [];
                  this._fill(fillRule, true);

                  this.ctx._clip_path = this.path;
                  this.path = origPath;
                  this._fill(fillRule, false);

                  lines.push('Q');
              } else {
                  this._fill(fillRule, false);
              }
          },

          _fill: function _fill(fillRule, isClip) {
              if (this._isFillTransparent()) {
                  return;
              }
              var v2Support = typeof this.pdf.internal.newObject2 === 'function';

              var lines;
              if (window.outIntercept) {
                  lines = window.outIntercept.type === 'group' ? window.outIntercept.stream : window.outIntercept;
              } else {
                  lines = this.pdf.internal.pages[1];
              }

              // if (this.ctx._clip_path.length > 0) {
              //     lines.push('q');
              //     var oldPath = this.path;
              //     this.path = this.ctx._clip_path;
              //     this.ctx._clip_path = [];
              //     this._fill(fillRule, true);
              //     this.ctx._clip_path = this.path;
              //     this.path = oldPath;
              // }

              var moves = [];
              var outInterceptOld = window.outIntercept;

              if (v2Support) {
                  // Blend and Mask
                  switch (this.ctx.globalCompositeOperation) {
                      case 'normal':
                      case 'source-over':
                          break;
                      case 'destination-in':
                      case 'destination-out':
                          //TODO this need to be added to the current group or page
                          // define a mask stream
                          var obj = this.pdf.internal.newStreamObject();

                          // define a mask state
                          var obj2 = this.pdf.internal.newObject2();
                          obj2.push('<</Type /ExtGState');
                          obj2.push('/SMask <</S /Alpha /G ' + obj.objId + ' 0 R>>'); // /S /Luminosity will need to define color space
                          obj2.push('>>');

                          // add mask to page resources
                          var gsName = 'MASK' + obj2.objId;
                          this.pdf.internal.addGraphicsState(gsName, obj2.objId);

                          var instruction = '/' + gsName + ' gs';
                          // add mask to page, group, or stream
                          lines.splice(0, 0, 'q');
                          lines.splice(1, 0, instruction);
                          lines.push('Q');

                          window.outIntercept = obj;
                          break;
                      default:
                          var dictionaryEntry = '/' + this.pdf.internal.blendModeMap[this.ctx.globalCompositeOperation.toUpperCase()];
                          if (dictionaryEntry) {
                              this.pdf.internal.out(dictionaryEntry + ' gs');
                          }
                          break;
                  }
              }

              var alpha = this.ctx.globalAlpha;
              if (this.ctx._fillOpacity < 1) {
                  // TODO combine this with global opacity
                  alpha = this.ctx._fillOpacity;
              }

              //TODO check for an opacity graphics state that was already created
              //TODO do not set opacity if current value is already active
              if (v2Support) {
                  var objOpac = this.pdf.internal.newObject2();
                  objOpac.push('<</Type /ExtGState');
                  //objOpac.push(this.ctx.globalAlpha + " CA"); // Stroke
                  //objOpac.push(this.ctx.globalAlpha + " ca"); // Not Stroke
                  objOpac.push('/CA ' + alpha); // Stroke
                  objOpac.push('/ca ' + alpha); // Not Stroke
                  objOpac.push('>>');
                  var gsName = 'GS_O_' + objOpac.objId;
                  this.pdf.internal.addGraphicsState(gsName, objOpac.objId);
                  this.pdf.internal.out('/' + gsName + ' gs');
              }

              var xPath = this.path;

              for (var i = 0; i < xPath.length; i++) {
                  var pt = xPath[i];
                  switch (pt.type) {
                      case 'mt':
                          moves.push({ start: pt, deltas: [], abs: [] });
                          break;
                      case 'lt':
                          var delta = [pt.x - xPath[i - 1].x, pt.y - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          moves[moves.length - 1].abs.push(pt);
                          break;
                      case 'bct':
                          var delta = [pt.x1 - xPath[i - 1].x, pt.y1 - xPath[i - 1].y, pt.x2 - xPath[i - 1].x, pt.y2 - xPath[i - 1].y, pt.x - xPath[i - 1].x, pt.y - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          break;
                      case 'qct':
                          // convert to bezier
                          var x1 = xPath[i - 1].x + 2.0 / 3.0 * (pt.x1 - xPath[i - 1].x);
                          var y1 = xPath[i - 1].y + 2.0 / 3.0 * (pt.y1 - xPath[i - 1].y);
                          var x2 = pt.x + 2.0 / 3.0 * (pt.x1 - pt.x);
                          var y2 = pt.y + 2.0 / 3.0 * (pt.y1 - pt.y);
                          var x3 = pt.x;
                          var y3 = pt.y;
                          var delta = [x1 - xPath[i - 1].x, y1 - xPath[i - 1].y, x2 - xPath[i - 1].x, y2 - xPath[i - 1].y, x3 - xPath[i - 1].x, y3 - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          break;
                      case 'arc':
                          //TODO this was hack to avoid out of bounds issue
                          if (moves.length == 0) {
                              moves.push({ start: { x: 0, y: 0 }, deltas: [], abs: [] });
                          }
                          moves[moves.length - 1].arc = true;
                          moves[moves.length - 1].abs.push(pt);
                          break;
                      case 'close':
                          //moves[moves.length - 1].deltas.push('close');
                          break;
                  }
              }

              for (var i = 0; i < moves.length; i++) {
                  var style;
                  if (i == moves.length - 1) {
                      style = 'f';
                      if (fillRule === 'evenodd') {
                          style += '*';
                      }
                  } else {
                      style = null;
                  }

                  if (moves[i].arc) {
                      var arcs = moves[i].abs;
                      for (var ii = 0; ii < arcs.length; ii++) {
                          var arc = arcs[ii];
                          //TODO lines deltas were getting in here
                          if (typeof arc.startAngle !== 'undefined') {
                              var start = arc.startAngle * 360 / (2 * Math.PI);
                              var end = arc.endAngle * 360 / (2 * Math.PI);
                              // Add the current position (last move to)
                              //var x = moves[i].start.x + arc.x;
                              //var y = moves[i].start.y + arc.y;
                              var x = arc.x;
                              var y = arc.y;
                              if (ii == 0) {
                                  this.internal.move2(this, x, y);
                              }
                              this.internal.arc2(this, x, y, arc.radius, start, end, arc.anticlockwise, null, isClip);
                          } else {
                              this.internal.line2(c2d, arc.x, arc.y);
                          }
                      }

                      // extra move bug causing close to resolve to wrong point
                      var x = moves[i].start.x;
                      var y = moves[i].start.y;
                      this.internal.line2(c2d, x, y);

                      this.pdf.internal.out('h');
                      this.pdf.internal.out('f');
                  } else {
                      var x = moves[i].start.x;
                      var y = moves[i].start.y;
                      if (!isClip) {
                          this.pdf.lines(moves[i].deltas, x, y, null, style);
                      } else {
                          this.pdf.lines(moves[i].deltas, x, y, null, null);
                          this.pdf.clip_fixed();
                      }
                  }
              }

              window.outIntercept = outInterceptOld;

              // if (this.ctx._clip_path.length > 0) {
              //     lines.push('Q');
              // }
          },

          pushMask: function pushMask() {
              var v2Support = typeof this.pdf.internal.newObject2 === 'function';

              if (!v2Support) {
                  console.log('jsPDF v2 not enabled');
                  return;
              }

              // define a mask stream
              var obj = this.pdf.internal.newStreamObject();

              // define a mask state
              var obj2 = this.pdf.internal.newObject2();
              obj2.push('<</Type /ExtGState');
              obj2.push('/SMask <</S /Alpha /G ' + obj.objId + ' 0 R>>'); // /S /Luminosity will need to define color space
              obj2.push('>>');

              // add mask to page resources
              var gsName = 'MASK' + obj2.objId;
              this.pdf.internal.addGraphicsState(gsName, obj2.objId);

              var instruction = '/' + gsName + ' gs';
              this.pdf.internal.out(instruction);
          },

          clip: function clip() {
              //TODO do we reset the path, or just copy it?
              if (this.ctx._clip_path.length > 0) {
                  for (var i = 0; i < this.path.length; i++) {
                      this.ctx._clip_path.push(this.path[i]);
                  }
              } else {
                  this.ctx._clip_path = this.path;
              }
              this.path = [];
          },

          measureText: function measureText(text) {
              var pdf = this.pdf;
              return {
                  getWidth: function getWidth() {
                      var fontSize = pdf.internal.getFontSize();
                      var txtWidth = pdf.getStringUnitWidth(text) * fontSize / pdf.internal.scaleFactor;
                      return txtWidth;
                  },

                  get width() {
                      return this.getWidth(text);
                  }
              };
          },
          _getBaseline: function _getBaseline(y) {
              var height = parseInt(this.pdf.internal.getFontSize());
              // TODO Get descent from font descriptor
              var descent = height * .25;
              switch (this.ctx.textBaseline) {
                  case 'bottom':
                      return y - descent;
                  case 'top':
                      return y + height;
                  case 'hanging':
                      return y + height - descent;
                  case 'middle':
                      return y + height / 2 - descent;
                  case 'ideographic':
                      // TODO not implemented
                      return y;
                  case 'alphabetic':
                  default:
                      return y;
              }
          }
      };

      var c2d = jsPDFAPI.context2d;

      // accessor methods
      Object.defineProperty(c2d, 'fillStyle', {
          set: function set(value) {
              this.setFillStyle(value);
          },
          get: function get() {
              return this.ctx.fillStyle;
          }
      });
      Object.defineProperty(c2d, 'strokeStyle', {
          set: function set(value) {
              this.setStrokeStyle(value);
          },
          get: function get() {
              return this.ctx.strokeStyle;
          }
      });
      Object.defineProperty(c2d, 'lineWidth', {
          set: function set(value) {
              this.setLineWidth(value);
          },
          get: function get() {
              return this.ctx.lineWidth;
          }
      });
      Object.defineProperty(c2d, 'lineCap', {
          set: function set(val) {
              this.setLineCap(val);
          },
          get: function get() {
              return this.ctx.lineCap;
          }
      });
      Object.defineProperty(c2d, 'lineJoin', {
          set: function set(val) {
              this.setLineJoin(val);
          },
          get: function get() {
              return this.ctx.lineJoin;
          }
      });
      Object.defineProperty(c2d, 'miterLimit', {
          set: function set(val) {
              this.ctx.miterLimit = val;
          },
          get: function get() {
              return this.ctx.miterLimit;
          }
      });
      Object.defineProperty(c2d, 'textBaseline', {
          set: function set(value) {
              this.setTextBaseline(value);
          },
          get: function get() {
              return this.getTextBaseline();
          }
      });
      Object.defineProperty(c2d, 'textAlign', {
          set: function set(value) {
              this.setTextAlign(value);
          },
          get: function get() {
              return this.getTextAlign();
          }
      });
      Object.defineProperty(c2d, 'font', {
          set: function set(value) {
              this.setFont(value);
          },
          get: function get() {
              return this.ctx.font;
          }
      });
      Object.defineProperty(c2d, 'globalCompositeOperation', {
          set: function set(value) {
              this.ctx.globalCompositeOperation = value;
          },
          get: function get() {
              return this.ctx.globalCompositeOperation;
          }
      });
      Object.defineProperty(c2d, 'globalAlpha', {
          set: function set(value) {
              this.ctx.globalAlpha = value;
          },
          get: function get() {
              return this.ctx.globalAlpha;
          }
      });

      c2d.internal = {};

      c2d.internal.rxRgb = /rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/;
      c2d.internal.rxRgba = /rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d\.]+)\s*\)/;
      c2d.internal.rxTransparent = /transparent|rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*0+\s*\)/;

      // http://hansmuller-flex.blogspot.com/2011/10/more-about-approximating-circular-arcs.html
      c2d.internal.arc = function (c2d, xc, yc, r, a1, a2, anticlockwise, style) {
          var includeMove = true;

          var k = this.pdf.internal.scaleFactor;
          var pageHeight = this.pdf.internal.pageSize.height;
          var f2 = this.pdf.internal.f2;

          var a1r = a1 * (Math.PI / 180);
          var a2r = a2 * (Math.PI / 180);
          var curves = this.createArc(r, a1r, a2r, anticlockwise);
          var pathData = null;

          for (var i = 0; i < curves.length; i++) {
              var curve = curves[i];
              if (includeMove && i == 0) {
                  this.pdf.internal.out([f2((curve.x1 + xc) * k), f2((pageHeight - (curve.y1 + yc)) * k), 'm', f2((curve.x2 + xc) * k), f2((pageHeight - (curve.y2 + yc)) * k), f2((curve.x3 + xc) * k), f2((pageHeight - (curve.y3 + yc)) * k), f2((curve.x4 + xc) * k), f2((pageHeight - (curve.y4 + yc)) * k), 'c'].join(' '));
              } else {
                  this.pdf.internal.out([f2((curve.x2 + xc) * k), f2((pageHeight - (curve.y2 + yc)) * k), f2((curve.x3 + xc) * k), f2((pageHeight - (curve.y3 + yc)) * k), f2((curve.x4 + xc) * k), f2((pageHeight - (curve.y4 + yc)) * k), 'c'].join(' '));
              }

              //c2d._lastPoint = {x: curve.x1 + xc, y: curve.y1 + yc};
              c2d._lastPoint = { x: xc, y: yc };
              // f2((curve.x1 + xc) * k), f2((pageHeight - (curve.y1 + yc)) * k), 'm', f2((curve.x2 + xc) * k), f2((pageHeight - (curve.y2 + yc)) * k), f2((curve.x3 + xc) * k), f2((pageHeight - (curve.y3 + yc)) * k), f2((curve.x4 + xc) * k), f2((pageHeight - (curve.y4 + yc)) * k), 'c'
          }

          if (style !== null) {
              this.pdf.internal.out(this.pdf.internal.getStyle(style));
          }
      };

      /**
       *
       * @param x Edge point X
       * @param y Edge point Y
       * @param r Radius
       * @param a1 start angle
       * @param a2 end angle
       * @param anticlockwise
       * @param style
       * @param isClip
       */
      c2d.internal.arc2 = function (c2d, x, y, r, a1, a2, anticlockwise, style, isClip) {
          // we need to convert from cartesian to polar here methinks.
          var centerX = x; // + r;
          var centerY = y;

          if (false) {}

          if (!isClip) {
              this.arc(c2d, centerX, centerY, r, a1, a2, anticlockwise, style);
          } else {
              this.arc(c2d, centerX, centerY, r, a1, a2, anticlockwise, null);
              this.pdf.clip_fixed();
          }
      };

      c2d.internal.move2 = function (c2d, x, y) {
          var k = this.pdf.internal.scaleFactor;
          var pageHeight = this.pdf.internal.pageSize.height;
          var f2 = this.pdf.internal.f2;

          this.pdf.internal.out([f2(x * k), f2((pageHeight - y) * k), 'm'].join(' '));
          c2d._lastPoint = { x: x, y: y };
      };

      c2d.internal.line2 = function (c2d, dx, dy) {
          var k = this.pdf.internal.scaleFactor;
          var pageHeight = this.pdf.internal.pageSize.height;
          var f2 = this.pdf.internal.f2;

          //var pt = {x: c2d._lastPoint.x + dx, y: c2d._lastPoint.y + dy};
          var pt = { x: dx, y: dy };

          this.pdf.internal.out([f2(pt.x * k), f2((pageHeight - pt.y) * k), 'l'].join(' '));
          //this.pdf.internal.out('f');
          c2d._lastPoint = pt;
      };

      /**
       * Return a array of objects that represent bezier curves which approximate the circular arc centered at the origin, from startAngle to endAngle (radians) with the specified radius.
       *
       * Each bezier curve is an object with four points, where x1,y1 and x4,y4 are the arc's end points and x2,y2 and x3,y3 are the cubic bezier's control points.
       */

      c2d.internal.createArc = function (radius, startAngle, endAngle, anticlockwise) {

          var EPSILON = 0.00001; // Roughly 1/1000th of a degree, see below

          // normalize startAngle, endAngle to [-2PI, 2PI]
          var twoPI = Math.PI * 2;
          var startAngleN = startAngle;
          if (startAngleN < twoPI || startAngleN > twoPI) {
              startAngleN = startAngleN % twoPI;
          }
          var endAngleN = endAngle;
          if (endAngleN < twoPI || endAngleN > twoPI) {
              endAngleN = endAngleN % twoPI;
          }

          // Compute the sequence of arc curves, up to PI/2 at a time.
          // Total arc angle is less than 2PI.
          var curves = [];
          var piOverTwo = Math.PI / 2.0;
          // var sgn = (startAngle < endAngle) ? +1 : -1; // clockwise or counterclockwise
          var sgn = anticlockwise ? -1 : +1;

          var a1 = startAngle;
          for (var totalAngle = Math.min(twoPI, Math.abs(endAngleN - startAngleN)); totalAngle > EPSILON;) {
              var a2 = a1 + sgn * Math.min(totalAngle, piOverTwo);
              curves.push(this.createSmallArc(radius, a1, a2));
              totalAngle -= Math.abs(a2 - a1);
              a1 = a2;
          }

          return curves;
      };

      /**
       * Cubic bezier approximation of a circular arc centered at the origin, from (radians) a1 to a2, where a2-a1 < pi/2. The arc's radius is r.
       *
       * Returns an object with four points, where x1,y1 and x4,y4 are the arc's end points and x2,y2 and x3,y3 are the cubic bezier's control points.
       *
       * This algorithm is based on the approach described in: A. Riškus, "Approximation of a Cubic Bezier Curve by Circular Arcs and Vice Versa," Information Technology and Control, 35(4), 2006 pp. 371-378.
       */

      c2d.internal.createSmallArc = function (r, a1, a2) {
          // Compute all four points for an arc that subtends the same total angle
          // but is centered on the X-axis

          var a = (a2 - a1) / 2.0;

          var x4 = r * Math.cos(a);
          var y4 = r * Math.sin(a);
          var x1 = x4;
          var y1 = -y4;

          var q1 = x1 * x1 + y1 * y1;
          var q2 = q1 + x1 * x4 + y1 * y4;
          var k2 = 4 / 3 * (Math.sqrt(2 * q1 * q2) - q2) / (x1 * y4 - y1 * x4);

          var x2 = x1 - k2 * y1;
          var y2 = y1 + k2 * x1;
          var x3 = x2;
          var y3 = -y2;

          // Find the arc points' actual locations by computing x1,y1 and x4,y4
          // and rotating the control points by a + a1

          var ar = a + a1;
          var cos_ar = Math.cos(ar);
          var sin_ar = Math.sin(ar);

          return {
              x1: r * Math.cos(a1),
              y1: r * Math.sin(a1),
              x2: x2 * cos_ar - y2 * sin_ar,
              y2: x2 * sin_ar + y2 * cos_ar,
              x3: x3 * cos_ar - y3 * sin_ar,
              y3: x3 * sin_ar + y3 * cos_ar,
              x4: r * Math.cos(a2),
              y4: r * Math.sin(a2)
          };
      };

      function context() {
          this._isStrokeTransparent = false;
          this._strokeOpacity = 1;
          this.strokeStyle = '#000000';
          this.fillStyle = '#000000';
          this._isFillTransparent = false;
          this._fillOpacity = 1;
          this.font = "12pt times";
          this.textBaseline = 'alphabetic'; // top,bottom,middle,ideographic,alphabetic,hanging
          this.textAlign = 'start';
          this.lineWidth = 1;
          this.lineJoin = 'miter'; // round, bevel, miter
          this.lineCap = 'butt'; // butt, round, square
          this._transform = [1, 0, 0, 1, 0, 0]; // sx, shy, shx, sy, tx, ty
          this.globalCompositeOperation = 'normal';
          this.globalAlpha = 1.0;
          this._clip_path = [];
          // TODO miter limit //default 10

          this.copy = function (ctx) {
              this._isStrokeTransparent = ctx._isStrokeTransparent;
              this._strokeOpacity = ctx._strokeOpacity;
              this.strokeStyle = ctx.strokeStyle;
              this._isFillTransparent = ctx._isFillTransparent;
              this._fillOpacity = ctx._fillOpacity;
              this.fillStyle = ctx.fillStyle;
              this.font = ctx.font;
              this.lineWidth = ctx.lineWidth;
              this.lineJoin = ctx.lineJoin;
              this.lineCap = ctx.lineCap;
              this.textBaseline = ctx.textBaseline;
              this.textAlign = ctx.textAlign;
              this._fontSize = ctx._fontSize;
              this._transform = ctx._transform.slice(0);
              this.globalCompositeOperation = ctx.globalCompositeOperation;
              this.globalAlpha = ctx.globalAlpha;
              this._clip_path = ctx._clip_path.slice(0); //TODO deep copy?
          };
      }

      return this;
  })(jsPDF.API);

  /** @preserve
   * jsPDF fromHTML plugin. BETA stage. API subject to change. Needs browser
   * Copyright (c) 2012 Willow Systems Corporation, willow-systems.com
   *               2014 Juan Pablo Gaviria, https://github.com/juanpgaviria
   *               2014 Diego Casorran, https://github.com/diegocr
   *               2014 Daniel Husar, https://github.com/danielhusar
   *               2014 Wolfgang Gassler, https://github.com/woolfg
   *               2014 Steven Spungin, https://github.com/flamenco
   *
   * 
   * ====================================================================
   */

  (function (jsPDFAPI) {
  	var clone, _DrillForContent, FontNameDB, FontStyleMap, TextAlignMap, FontWeightMap, FloatMap, ClearMap, GetCSS, PurgeWhiteSpace, Renderer, ResolveFont, ResolveUnitedNumber, UnitedNumberMap, elementHandledElsewhere, images, loadImgs, checkForFooter, process, tableToJson;
  	clone = function () {
  		return function (obj) {
  			Clone.prototype = obj;
  			return new Clone();
  		};
  		function Clone() {}
  	}();
  	PurgeWhiteSpace = function PurgeWhiteSpace(array) {
  		var fragment, i, l, lTrimmed, r, rTrimmed, trailingSpace;
  		i = 0;
  		l = array.length;
  		fragment = void 0;
  		lTrimmed = false;
  		rTrimmed = false;
  		while (!lTrimmed && i !== l) {
  			fragment = array[i] = array[i].trimLeft();
  			if (fragment) {
  				lTrimmed = true;
  			}
  			i++;
  		}
  		i = l - 1;
  		while (l && !rTrimmed && i !== -1) {
  			fragment = array[i] = array[i].trimRight();
  			if (fragment) {
  				rTrimmed = true;
  			}
  			i--;
  		}
  		r = /\s+$/g;
  		trailingSpace = true;
  		i = 0;
  		while (i !== l) {
  			// Leave the line breaks intact
  			if (array[i] != "\u2028") {
  				fragment = array[i].replace(/\s+/g, " ");
  				if (trailingSpace) {
  					fragment = fragment.trimLeft();
  				}
  				if (fragment) {
  					trailingSpace = r.test(fragment);
  				}
  				array[i] = fragment;
  			}
  			i++;
  		}
  		return array;
  	};
  	Renderer = function Renderer(pdf, x, y, settings) {
  		this.pdf = pdf;
  		this.x = x;
  		this.y = y;
  		this.settings = settings;
  		//list of functions which are called after each element-rendering process
  		this.watchFunctions = [];
  		this.init();
  		return this;
  	};
  	ResolveFont = function ResolveFont(css_font_family_string) {
  		var name, part, parts;
  		name = void 0;
  		parts = css_font_family_string.split(",");
  		part = parts.shift();
  		while (!name && part) {
  			name = FontNameDB[part.trim().toLowerCase()];
  			part = parts.shift();
  		}
  		return name;
  	};
  	ResolveUnitedNumber = function ResolveUnitedNumber(css_line_height_string) {

  		//IE8 issues
  		css_line_height_string = css_line_height_string === "auto" ? "0px" : css_line_height_string;
  		if (css_line_height_string.indexOf("em") > -1 && !isNaN(Number(css_line_height_string.replace("em", "")))) {
  			css_line_height_string = Number(css_line_height_string.replace("em", "")) * 18.719 + "px";
  		}
  		if (css_line_height_string.indexOf("pt") > -1 && !isNaN(Number(css_line_height_string.replace("pt", "")))) {
  			css_line_height_string = Number(css_line_height_string.replace("pt", "")) * 1.333 + "px";
  		}

  		var normal, undef, value;
  		undef = void 0;
  		normal = 16.00;
  		value = UnitedNumberMap[css_line_height_string];
  		if (value) {
  			return value;
  		}
  		value = {
  			"xx-small": 9,
  			"x-small": 11,
  			small: 13,
  			medium: 16,
  			large: 19,
  			"x-large": 23,
  			"xx-large": 28,
  			auto: 0
  		}[{ css_line_height_string: css_line_height_string }];

  		if (value !== undef) {
  			return UnitedNumberMap[css_line_height_string] = value / normal;
  		}
  		if (value = parseFloat(css_line_height_string)) {
  			return UnitedNumberMap[css_line_height_string] = value / normal;
  		}
  		value = css_line_height_string.match(/([\d\.]+)(px)/);
  		if (value.length === 3) {
  			return UnitedNumberMap[css_line_height_string] = parseFloat(value[1]) / normal;
  		}
  		return UnitedNumberMap[css_line_height_string] = 1;
  	};
  	GetCSS = function GetCSS(element) {
  		var css, tmp, computedCSSElement;
  		computedCSSElement = function (el) {
  			var compCSS;
  			compCSS = function (el) {
  				if (document.defaultView && document.defaultView.getComputedStyle) {
  					return document.defaultView.getComputedStyle(el, null);
  				} else if (el.currentStyle) {
  					return el.currentStyle;
  				} else {
  					return el.style;
  				}
  			}(el);
  			return function (prop) {
  				prop = prop.replace(/-\D/g, function (match) {
  					return match.charAt(1).toUpperCase();
  				});
  				return compCSS[prop];
  			};
  		}(element);
  		css = {};
  		tmp = void 0;
  		css["font-family"] = ResolveFont(computedCSSElement("font-family")) || "times";
  		css["font-style"] = FontStyleMap[computedCSSElement("font-style")] || "normal";
  		css["text-align"] = TextAlignMap[computedCSSElement("text-align")] || "left";
  		tmp = FontWeightMap[computedCSSElement("font-weight")] || "normal";
  		if (tmp === "bold") {
  			if (css["font-style"] === "normal") {
  				css["font-style"] = tmp;
  			} else {
  				css["font-style"] = tmp + css["font-style"];
  			}
  		}
  		css["font-size"] = ResolveUnitedNumber(computedCSSElement("font-size")) || 1;
  		css["line-height"] = ResolveUnitedNumber(computedCSSElement("line-height")) || 1;
  		css["display"] = computedCSSElement("display") === "inline" ? "inline" : "block";

  		tmp = css["display"] === "block";
  		css["margin-top"] = tmp && ResolveUnitedNumber(computedCSSElement("margin-top")) || 0;
  		css["margin-bottom"] = tmp && ResolveUnitedNumber(computedCSSElement("margin-bottom")) || 0;
  		css["padding-top"] = tmp && ResolveUnitedNumber(computedCSSElement("padding-top")) || 0;
  		css["padding-bottom"] = tmp && ResolveUnitedNumber(computedCSSElement("padding-bottom")) || 0;
  		css["margin-left"] = tmp && ResolveUnitedNumber(computedCSSElement("margin-left")) || 0;
  		css["margin-right"] = tmp && ResolveUnitedNumber(computedCSSElement("margin-right")) || 0;
  		css["padding-left"] = tmp && ResolveUnitedNumber(computedCSSElement("padding-left")) || 0;
  		css["padding-right"] = tmp && ResolveUnitedNumber(computedCSSElement("padding-right")) || 0;

  		css["page-break-before"] = computedCSSElement("page-break-before") || "auto";

  		//float and clearing of floats
  		css["float"] = FloatMap[computedCSSElement("cssFloat")] || "none";
  		css["clear"] = ClearMap[computedCSSElement("clear")] || "none";

  		css["color"] = computedCSSElement("color");

  		return css;
  	};
  	elementHandledElsewhere = function elementHandledElsewhere(element, renderer, elementHandlers) {
  		var handlers, i, isHandledElsewhere, l, t;
  		isHandledElsewhere = false;
  		i = void 0;
  		l = void 0;
  		t = void 0;
  		handlers = elementHandlers["#" + element.id];
  		if (handlers) {
  			if (typeof handlers === "function") {
  				isHandledElsewhere = handlers(element, renderer);
  			} else {
  				i = 0;
  				l = handlers.length;
  				while (!isHandledElsewhere && i !== l) {
  					isHandledElsewhere = handlers[i](element, renderer);
  					i++;
  				}
  			}
  		}
  		handlers = elementHandlers[element.nodeName];
  		if (!isHandledElsewhere && handlers) {
  			if (typeof handlers === "function") {
  				isHandledElsewhere = handlers(element, renderer);
  			} else {
  				i = 0;
  				l = handlers.length;
  				while (!isHandledElsewhere && i !== l) {
  					isHandledElsewhere = handlers[i](element, renderer);
  					i++;
  				}
  			}
  		}
  		return isHandledElsewhere;
  	};
  	tableToJson = function tableToJson(table, renderer) {
  		var data, headers, i, j, rowData, tableRow, table_obj, table_with, cell, l;
  		data = [];
  		headers = [];
  		i = 0;
  		l = table.rows[0].cells.length;
  		table_with = table.clientWidth;
  		while (i < l) {
  			cell = table.rows[0].cells[i];
  			headers[i] = {
  				name: cell.textContent.toLowerCase().replace(/\s+/g, ''),
  				prompt: cell.textContent.replace(/\r?\n/g, ''),
  				width: cell.clientWidth / table_with * renderer.pdf.internal.pageSize.width
  			};
  			i++;
  		}
  		i = 1;
  		while (i < table.rows.length) {
  			tableRow = table.rows[i];
  			rowData = {};
  			j = 0;
  			while (j < tableRow.cells.length) {
  				rowData[headers[j].name] = tableRow.cells[j].textContent.replace(/\r?\n/g, '');
  				j++;
  			}
  			data.push(rowData);
  			i++;
  		}
  		return table_obj = {
  			rows: data,
  			headers: headers
  		};
  	};
  	var SkipNode = {
  		SCRIPT: 1,
  		STYLE: 1,
  		NOSCRIPT: 1,
  		OBJECT: 1,
  		EMBED: 1,
  		SELECT: 1
  	};
  	var listCount = 1;
  	_DrillForContent = function DrillForContent(element, renderer, elementHandlers) {
  		var cn, cns, fragmentCSS, i, isBlock, l, px2pt, table2json, cb;
  		cns = element.childNodes;
  		cn = void 0;
  		fragmentCSS = GetCSS(element);
  		isBlock = fragmentCSS.display === "block";
  		if (isBlock) {
  			renderer.setBlockBoundary();
  			renderer.setBlockStyle(fragmentCSS);
  		}
  		px2pt = 0.264583 * 72 / 25.4;
  		i = 0;
  		l = cns.length;
  		while (i < l) {
  			cn = cns[i];
  			if ((typeof cn === "undefined" ? "undefined" : _typeof(cn)) === "object") {

  				//execute all watcher functions to e.g. reset floating
  				renderer.executeWatchFunctions(cn);

  				/*** HEADER rendering **/
  				if (cn.nodeType === 1 && cn.nodeName === 'HEADER') {
  					var header = cn;
  					//store old top margin
  					var oldMarginTop = renderer.pdf.margins_doc.top;
  					//subscribe for new page event and render header first on every page
  					renderer.pdf.internal.events.subscribe('addPage', function (pageInfo) {
  						//set current y position to old margin
  						renderer.y = oldMarginTop;
  						//render all child nodes of the header element
  						_DrillForContent(header, renderer, elementHandlers);
  						//set margin to old margin + rendered header + 10 space to prevent overlapping
  						//important for other plugins (e.g. table) to start rendering at correct position after header
  						renderer.pdf.margins_doc.top = renderer.y + 10;
  						renderer.y += 10;
  					}, false);
  				}

  				if (cn.nodeType === 8 && cn.nodeName === "#comment") {
  					if (~cn.textContent.indexOf("ADD_PAGE")) {
  						renderer.pdf.addPage();
  						renderer.y = renderer.pdf.margins_doc.top;
  					}
  				} else if (cn.nodeType === 1 && !SkipNode[cn.nodeName]) {
  					/*** IMAGE RENDERING ***/
  					var cached_image;
  					if (cn.nodeName === "IMG") {
  						var url = cn.getAttribute("src");
  						cached_image = images[renderer.pdf.sHashCode(url) || url];
  					}
  					if (cached_image) {
  						if (renderer.pdf.internal.pageSize.height - renderer.pdf.margins_doc.bottom < renderer.y + cn.height && renderer.y > renderer.pdf.margins_doc.top) {
  							renderer.pdf.addPage();
  							renderer.y = renderer.pdf.margins_doc.top;
  							//check if we have to set back some values due to e.g. header rendering for new page
  							renderer.executeWatchFunctions(cn);
  						}

  						var imagesCSS = GetCSS(cn);
  						var imageX = renderer.x;
  						var fontToUnitRatio = 12 / renderer.pdf.internal.scaleFactor;

  						//define additional paddings, margins which have to be taken into account for margin calculations
  						var additionalSpaceLeft = (imagesCSS["margin-left"] + imagesCSS["padding-left"]) * fontToUnitRatio;
  						var additionalSpaceRight = (imagesCSS["margin-right"] + imagesCSS["padding-right"]) * fontToUnitRatio;
  						var additionalSpaceTop = (imagesCSS["margin-top"] + imagesCSS["padding-top"]) * fontToUnitRatio;
  						var additionalSpaceBottom = (imagesCSS["margin-bottom"] + imagesCSS["padding-bottom"]) * fontToUnitRatio;

  						//if float is set to right, move the image to the right border
  						//add space if margin is set
  						if (imagesCSS['float'] !== undefined && imagesCSS['float'] === 'right') {
  							imageX += renderer.settings.width - cn.width - additionalSpaceRight;
  						} else {
  							imageX += additionalSpaceLeft;
  						}

  						renderer.pdf.addImage(cached_image, imageX, renderer.y + additionalSpaceTop, cn.width, cn.height);
  						cached_image = undefined;
  						//if the float prop is specified we have to float the text around the image
  						if (imagesCSS['float'] === 'right' || imagesCSS['float'] === 'left') {
  							//add functiont to set back coordinates after image rendering
  							renderer.watchFunctions.push(function (diffX, thresholdY, diffWidth, el) {
  								//undo drawing box adaptions which were set by floating
  								if (renderer.y >= thresholdY) {
  									renderer.x += diffX;
  									renderer.settings.width += diffWidth;
  									return true;
  								} else if (el && el.nodeType === 1 && !SkipNode[el.nodeName] && renderer.x + el.width > renderer.pdf.margins_doc.left + renderer.pdf.margins_doc.width) {
  									renderer.x += diffX;
  									renderer.y = thresholdY;
  									renderer.settings.width += diffWidth;
  									return true;
  								} else {
  									return false;
  								}
  							}.bind(this, imagesCSS['float'] === 'left' ? -cn.width - additionalSpaceLeft - additionalSpaceRight : 0, renderer.y + cn.height + additionalSpaceTop + additionalSpaceBottom, cn.width));
  							//reset floating by clear:both divs
  							//just set cursorY after the floating element
  							renderer.watchFunctions.push(function (yPositionAfterFloating, pages, el) {
  								if (renderer.y < yPositionAfterFloating && pages === renderer.pdf.internal.getNumberOfPages()) {
  									if (el.nodeType === 1 && GetCSS(el).clear === 'both') {
  										renderer.y = yPositionAfterFloating;
  										return true;
  									} else {
  										return false;
  									}
  								} else {
  									return true;
  								}
  							}.bind(this, renderer.y + cn.height, renderer.pdf.internal.getNumberOfPages()));

  							//if floating is set we decrease the available width by the image width
  							renderer.settings.width -= cn.width + additionalSpaceLeft + additionalSpaceRight;
  							//if left just add the image width to the X coordinate
  							if (imagesCSS['float'] === 'left') {
  								renderer.x += cn.width + additionalSpaceLeft + additionalSpaceRight;
  							}
  						} else {
  							//if no floating is set, move the rendering cursor after the image height
  							renderer.y += cn.height + additionalSpaceTop + additionalSpaceBottom;
  						}

  						/*** TABLE RENDERING ***/
  					} else if (cn.nodeName === "TABLE") {
  						table2json = tableToJson(cn, renderer);
  						renderer.y += 10;
  						renderer.pdf.table(renderer.x, renderer.y, table2json.rows, table2json.headers, {
  							autoSize: false,
  							printHeaders: elementHandlers.printHeaders,
  							margins: renderer.pdf.margins_doc,
  							css: GetCSS(cn)
  						});
  						renderer.y = renderer.pdf.lastCellPos.y + renderer.pdf.lastCellPos.h + 20;
  					} else if (cn.nodeName === "OL" || cn.nodeName === "UL") {
  						listCount = 1;
  						if (!elementHandledElsewhere(cn, renderer, elementHandlers)) {
  							_DrillForContent(cn, renderer, elementHandlers);
  						}
  						renderer.y += 10;
  					} else if (cn.nodeName === "LI") {
  						var temp = renderer.x;
  						renderer.x += 20 / renderer.pdf.internal.scaleFactor;
  						renderer.y += 3;
  						if (!elementHandledElsewhere(cn, renderer, elementHandlers)) {
  							_DrillForContent(cn, renderer, elementHandlers);
  						}
  						renderer.x = temp;
  					} else if (cn.nodeName === "BR") {
  						renderer.y += fragmentCSS["font-size"] * renderer.pdf.internal.scaleFactor;
  						renderer.addText("\u2028", clone(fragmentCSS));
  					} else {
  						if (!elementHandledElsewhere(cn, renderer, elementHandlers)) {
  							_DrillForContent(cn, renderer, elementHandlers);
  						}
  					}
  				} else if (cn.nodeType === 3) {
  					var value = cn.nodeValue;
  					if (cn.nodeValue && cn.parentNode.nodeName === "LI") {
  						if (cn.parentNode.parentNode.nodeName === "OL") {
  							value = listCount++ + '. ' + value;
  						} else {
  							var fontSize = fragmentCSS["font-size"];
  							var offsetX = (3 - fontSize * 0.75) * renderer.pdf.internal.scaleFactor;
  							var offsetY = fontSize * 0.75 * renderer.pdf.internal.scaleFactor;
  							var radius = fontSize * 1.74 / renderer.pdf.internal.scaleFactor;
  							cb = function cb(x, y) {
  								this.pdf.circle(x + offsetX, y + offsetY, radius, 'FD');
  							};
  						}
  					}
  					// Only add the text if the text node is in the body element
  					// Add compatibility with IE11
  					if (!!(cn.ownerDocument.body.compareDocumentPosition(cn) & 16)) {
  						renderer.addText(value, fragmentCSS);
  					}
  				} else if (typeof cn === "string") {
  					renderer.addText(cn, fragmentCSS);
  				}
  			}
  			i++;
  		}
  		elementHandlers.outY = renderer.y;

  		if (isBlock) {
  			return renderer.setBlockBoundary(cb);
  		}
  	};
  	images = {};
  	loadImgs = function loadImgs(element, renderer, elementHandlers, cb) {
  		var imgs = element.getElementsByTagName('img'),
  		    l = imgs.length,
  		    found_images,
  		    x = 0;
  		function done() {
  			renderer.pdf.internal.events.publish('imagesLoaded');
  			cb(found_images);
  		}
  		function loadImage(url, width, height) {
  			if (!url) return;
  			var img = new Image();
  			found_images = ++x;
  			img.crossOrigin = '';
  			img.onerror = img.onload = function () {
  				if (img.complete) {
  					//to support data urls in images, set width and height
  					//as those values are not recognized automatically
  					if (img.src.indexOf('data:image/') === 0) {
  						img.width = width || img.width || 0;
  						img.height = height || img.height || 0;
  					}
  					//if valid image add to known images array
  					if (img.width + img.height) {
  						var hash = renderer.pdf.sHashCode(url) || url;
  						images[hash] = images[hash] || img;
  					}
  				}
  				if (! --x) {
  					done();
  				}
  			};
  			img.src = url;
  		}
  		while (l--) {
  			loadImage(imgs[l].getAttribute("src"), imgs[l].width, imgs[l].height);
  		}return x || done();
  	};
  	checkForFooter = function checkForFooter(elem, renderer, elementHandlers) {
  		//check if we can found a <footer> element
  		var footer = elem.getElementsByTagName("footer");
  		if (footer.length > 0) {

  			footer = footer[0];

  			//bad hack to get height of footer
  			//creat dummy out and check new y after fake rendering
  			var oldOut = renderer.pdf.internal.write;
  			var oldY = renderer.y;
  			renderer.pdf.internal.write = function () {};
  			_DrillForContent(footer, renderer, elementHandlers);
  			var footerHeight = Math.ceil(renderer.y - oldY) + 5;
  			renderer.y = oldY;
  			renderer.pdf.internal.write = oldOut;

  			//add 20% to prevent overlapping
  			renderer.pdf.margins_doc.bottom += footerHeight;

  			//Create function render header on every page
  			var renderFooter = function renderFooter(pageInfo) {
  				var pageNumber = pageInfo !== undefined ? pageInfo.pageNumber : 1;
  				//set current y position to old margin
  				var oldPosition = renderer.y;
  				//render all child nodes of the header element
  				renderer.y = renderer.pdf.internal.pageSize.height - renderer.pdf.margins_doc.bottom;
  				renderer.pdf.margins_doc.bottom -= footerHeight;

  				//check if we have to add page numbers
  				var spans = footer.getElementsByTagName('span');
  				for (var i = 0; i < spans.length; ++i) {
  					//if we find some span element with class pageCounter, set the page
  					if ((" " + spans[i].className + " ").replace(/[\n\t]/g, " ").indexOf(" pageCounter ") > -1) {
  						spans[i].innerHTML = pageNumber;
  					}
  					//if we find some span element with class totalPages, set a variable which is replaced after rendering of all pages
  					if ((" " + spans[i].className + " ").replace(/[\n\t]/g, " ").indexOf(" totalPages ") > -1) {
  						spans[i].innerHTML = '###jsPDFVarTotalPages###';
  					}
  				}

  				//render footer content
  				_DrillForContent(footer, renderer, elementHandlers);
  				//set bottom margin to previous height including the footer height
  				renderer.pdf.margins_doc.bottom += footerHeight;
  				//important for other plugins (e.g. table) to start rendering at correct position after header
  				renderer.y = oldPosition;
  			};

  			//check if footer contains totalPages which should be replace at the disoposal of the document
  			var spans = footer.getElementsByTagName('span');
  			for (var i = 0; i < spans.length; ++i) {
  				if ((" " + spans[i].className + " ").replace(/[\n\t]/g, " ").indexOf(" totalPages ") > -1) {
  					renderer.pdf.internal.events.subscribe('htmlRenderingFinished', renderer.pdf.putTotalPages.bind(renderer.pdf, '###jsPDFVarTotalPages###'), true);
  				}
  			}

  			//register event to render footer on every new page
  			renderer.pdf.internal.events.subscribe('addPage', renderFooter, false);
  			//render footer on first page
  			renderFooter();

  			//prevent footer rendering
  			SkipNode['FOOTER'] = 1;
  		}
  	};
  	process = function process(pdf, element, x, y, settings, callback) {
  		if (!element) return false;
  		if (typeof element !== "string" && !element.parentNode) element = '' + element.innerHTML;
  		if (typeof element === "string") {
  			element = function (element) {
  				var $frame, $hiddendiv, framename, visuallyhidden;
  				framename = "jsPDFhtmlText" + Date.now().toString() + (Math.random() * 1000).toFixed(0);
  				visuallyhidden = "position: absolute !important;" + "clip: rect(1px 1px 1px 1px); /* IE6, IE7 */" + "clip: rect(1px, 1px, 1px, 1px);" + "padding:0 !important;" + "border:0 !important;" + "height: 1px !important;" + "width: 1px !important; " + "top:auto;" + "left:-100px;" + "overflow: hidden;";
  				$hiddendiv = document.createElement('div');
  				$hiddendiv.style.cssText = visuallyhidden;
  				$hiddendiv.innerHTML = "<iframe style=\"height:1px;width:1px\" name=\"" + framename + "\" />";
  				document.body.appendChild($hiddendiv);
  				$frame = window.frames[framename];
  				$frame.document.open();
  				$frame.document.writeln(element);
  				$frame.document.close();
  				return $frame.document.body;
  			}(element.replace(/<\/?script[^>]*?>/gi, ''));
  		}
  		var r = new Renderer(pdf, x, y, settings),
  		    out;

  		// 1. load images
  		// 2. prepare optional footer elements
  		// 3. render content
  		loadImgs.call(this, element, r, settings.elementHandlers, function (found_images) {
  			checkForFooter(element, r, settings.elementHandlers);
  			_DrillForContent(element, r, settings.elementHandlers);
  			//send event dispose for final taks (e.g. footer totalpage replacement)
  			r.pdf.internal.events.publish('htmlRenderingFinished');
  			out = r.dispose();
  			if (typeof callback === 'function') callback(out);else if (found_images) console.error('jsPDF Warning: rendering issues? provide a callback to fromHTML!');
  		});
  		return out || { x: r.x, y: r.y };
  	};
  	Renderer.prototype.init = function () {
  		this.paragraph = {
  			text: [],
  			style: []
  		};
  		return this.pdf.internal.write("q");
  	};
  	Renderer.prototype.dispose = function () {
  		this.pdf.internal.write("Q");
  		return {
  			x: this.x,
  			y: this.y,
  			ready: true
  		};
  	};

  	//Checks if we have to execute some watcher functions
  	//e.g. to end text floating around an image
  	Renderer.prototype.executeWatchFunctions = function (el) {
  		var ret = false;
  		var narray = [];
  		if (this.watchFunctions.length > 0) {
  			for (var i = 0; i < this.watchFunctions.length; ++i) {
  				if (this.watchFunctions[i](el) === true) {
  					ret = true;
  				} else {
  					narray.push(this.watchFunctions[i]);
  				}
  			}
  			this.watchFunctions = narray;
  		}
  		return ret;
  	};

  	Renderer.prototype.splitFragmentsIntoLines = function (fragments, styles) {
  		var currentLineLength, defaultFontSize, ff, fontMetrics, fontMetricsCache, fragment, fragmentChopped, fragmentLength, fragmentSpecificMetrics, fs, k, line, lines, maxLineLength, style;
  		defaultFontSize = 12;
  		k = this.pdf.internal.scaleFactor;
  		fontMetricsCache = {};
  		ff = void 0;
  		fs = void 0;
  		fontMetrics = void 0;
  		fragment = void 0;
  		style = void 0;
  		fragmentSpecificMetrics = void 0;
  		fragmentLength = void 0;
  		fragmentChopped = void 0;
  		line = [];
  		lines = [line];
  		currentLineLength = 0;
  		maxLineLength = this.settings.width;
  		while (fragments.length) {
  			fragment = fragments.shift();
  			style = styles.shift();
  			if (fragment) {
  				ff = style["font-family"];
  				fs = style["font-style"];
  				fontMetrics = fontMetricsCache[ff + fs];
  				if (!fontMetrics) {
  					fontMetrics = this.pdf.internal.getFont(ff, fs).metadata.Unicode;
  					fontMetricsCache[ff + fs] = fontMetrics;
  				}
  				fragmentSpecificMetrics = {
  					widths: fontMetrics.widths,
  					kerning: fontMetrics.kerning,
  					fontSize: style["font-size"] * defaultFontSize,
  					textIndent: currentLineLength
  				};
  				fragmentLength = this.pdf.getStringUnitWidth(fragment, fragmentSpecificMetrics) * fragmentSpecificMetrics.fontSize / k;
  				if (fragment == "\u2028") {
  					line = [];
  					lines.push(line);
  				} else if (currentLineLength + fragmentLength > maxLineLength) {
  					fragmentChopped = this.pdf.splitTextToSize(fragment, maxLineLength, fragmentSpecificMetrics);
  					line.push([fragmentChopped.shift(), style]);
  					while (fragmentChopped.length) {
  						line = [[fragmentChopped.shift(), style]];
  						lines.push(line);
  					}
  					currentLineLength = this.pdf.getStringUnitWidth(line[0][0], fragmentSpecificMetrics) * fragmentSpecificMetrics.fontSize / k;
  				} else {
  					line.push([fragment, style]);
  					currentLineLength += fragmentLength;
  				}
  			}
  		}

  		//if text alignment was set, set margin/indent of each line
  		if (style['text-align'] !== undefined && (style['text-align'] === 'center' || style['text-align'] === 'right' || style['text-align'] === 'justify')) {
  			for (var i = 0; i < lines.length; ++i) {
  				var length = this.pdf.getStringUnitWidth(lines[i][0][0], fragmentSpecificMetrics) * fragmentSpecificMetrics.fontSize / k;
  				//if there is more than on line we have to clone the style object as all lines hold a reference on this object
  				if (i > 0) {
  					lines[i][0][1] = clone(lines[i][0][1]);
  				}
  				var space = maxLineLength - length;

  				if (style['text-align'] === 'right') {
  					lines[i][0][1]['margin-left'] = space;
  					//if alignment is not right, it has to be center so split the space to the left and the right
  				} else if (style['text-align'] === 'center') {
  					lines[i][0][1]['margin-left'] = space / 2;
  					//if justify was set, calculate the word spacing and define in by using the css property
  				} else if (style['text-align'] === 'justify') {
  					var countSpaces = lines[i][0][0].split(' ').length - 1;
  					lines[i][0][1]['word-spacing'] = space / countSpaces;
  					//ignore the last line in justify mode
  					if (i === lines.length - 1) {
  						lines[i][0][1]['word-spacing'] = 0;
  					}
  				}
  			}
  		}

  		return lines;
  	};
  	Renderer.prototype.RenderTextFragment = function (text, style) {
  		var defaultFontSize, font, maxLineHeight;

  		maxLineHeight = 0;
  		defaultFontSize = 12;

  		if (this.pdf.internal.pageSize.height - this.pdf.margins_doc.bottom < this.y + this.pdf.internal.getFontSize()) {
  			this.pdf.internal.write("ET", "Q");
  			this.pdf.addPage();
  			this.y = this.pdf.margins_doc.top;
  			this.pdf.internal.write("q", "BT 0 g", this.pdf.internal.getCoordinateString(this.x), this.pdf.internal.getVerticalCoordinateString(this.y), style.color, "Td");
  			//move cursor by one line on new page
  			maxLineHeight = Math.max(maxLineHeight, style["line-height"], style["font-size"]);
  			this.pdf.internal.write(0, (-1 * defaultFontSize * maxLineHeight).toFixed(2), "Td");
  		}

  		font = this.pdf.internal.getFont(style["font-family"], style["font-style"]);

  		// text color
  		var pdfTextColor = this.getPdfColor(style["color"]);
  		if (pdfTextColor !== this.lastTextColor) {
  			this.pdf.internal.write(pdfTextColor);
  			this.lastTextColor = pdfTextColor;
  		}

  		//set the word spacing for e.g. justify style
  		if (style['word-spacing'] !== undefined && style['word-spacing'] > 0) {
  			this.pdf.internal.write(style['word-spacing'].toFixed(2), "Tw");
  		}

  		this.pdf.internal.write("/" + font.id, (defaultFontSize * style["font-size"]).toFixed(2), "Tf", "(" + this.pdf.internal.pdfEscape(text) + ") Tj");

  		//set the word spacing back to neutral => 0
  		if (style['word-spacing'] !== undefined) {
  			this.pdf.internal.write(0, "Tw");
  		}
  	};

  	// Accepts #FFFFFF, rgb(int,int,int), or CSS Color Name
  	Renderer.prototype.getPdfColor = function (style) {
  		var textColor;
  		var r, g, b;

  		var rx = /rgb\s*\(\s*(\d+),\s*(\d+),\s*(\d+\s*)\)/;
  		var m = rx.exec(style);
  		if (m != null) {
  			r = parseInt(m[1]);
  			g = parseInt(m[2]);
  			b = parseInt(m[3]);
  		} else {
  			if (style.charAt(0) != '#') {
  				style = CssColors.colorNameToHex(style);
  				if (!style) {
  					style = '#000000';
  				}
  			}
  			r = style.substring(1, 3);
  			r = parseInt(r, 16);
  			g = style.substring(3, 5);
  			g = parseInt(g, 16);
  			b = style.substring(5, 7);
  			b = parseInt(b, 16);
  		}

  		if (typeof r === 'string' && /^#[0-9A-Fa-f]{6}$/.test(r)) {
  			var hex = parseInt(r.substr(1), 16);
  			r = hex >> 16 & 255;
  			g = hex >> 8 & 255;
  			b = hex & 255;
  		}

  		var f3 = this.f3;
  		if (r === 0 && g === 0 && b === 0 || typeof g === 'undefined') {
  			textColor = f3(r / 255) + ' g';
  		} else {
  			textColor = [f3(r / 255), f3(g / 255), f3(b / 255), 'rg'].join(' ');
  		}
  		return textColor;
  	};

  	Renderer.prototype.f3 = function (number) {
  		return number.toFixed(3); // Ie, %.3f
  	}, Renderer.prototype.renderParagraph = function (cb) {
  		var blockstyle, defaultFontSize, fontToUnitRatio, fragments, i, l, line, lines, maxLineHeight, out, paragraphspacing_after, paragraphspacing_before, priorblockstyle, styles, fontSize;
  		fragments = PurgeWhiteSpace(this.paragraph.text);
  		styles = this.paragraph.style;
  		blockstyle = this.paragraph.blockstyle;
  		priorblockstyle = this.paragraph.priorblockstyle || {};
  		this.paragraph = {
  			text: [],
  			style: [],
  			blockstyle: {},
  			priorblockstyle: blockstyle
  		};
  		if (!fragments.join("").trim()) {
  			return;
  		}
  		lines = this.splitFragmentsIntoLines(fragments, styles);
  		line = void 0;
  		maxLineHeight = void 0;
  		defaultFontSize = 12;
  		fontToUnitRatio = defaultFontSize / this.pdf.internal.scaleFactor;
  		this.priorMarginBottom = this.priorMarginBottom || 0;
  		paragraphspacing_before = (Math.max((blockstyle["margin-top"] || 0) - this.priorMarginBottom, 0) + (blockstyle["padding-top"] || 0)) * fontToUnitRatio;
  		paragraphspacing_after = ((blockstyle["margin-bottom"] || 0) + (blockstyle["padding-bottom"] || 0)) * fontToUnitRatio;
  		this.priorMarginBottom = blockstyle["margin-bottom"] || 0;

  		if (blockstyle['page-break-before'] === 'always') {
  			this.pdf.addPage();
  			this.y = 0;
  			paragraphspacing_before = ((blockstyle["margin-top"] || 0) + (blockstyle["padding-top"] || 0)) * fontToUnitRatio;
  		}

  		out = this.pdf.internal.write;
  		i = void 0;
  		l = void 0;
  		this.y += paragraphspacing_before;
  		out("q", "BT 0 g", this.pdf.internal.getCoordinateString(this.x), this.pdf.internal.getVerticalCoordinateString(this.y), "Td");

  		//stores the current indent of cursor position
  		var currentIndent = 0;

  		while (lines.length) {
  			line = lines.shift();
  			maxLineHeight = 0;
  			i = 0;
  			l = line.length;
  			while (i !== l) {
  				if (line[i][0].trim()) {
  					maxLineHeight = Math.max(maxLineHeight, line[i][1]["line-height"], line[i][1]["font-size"]);
  					fontSize = line[i][1]["font-size"] * 7;
  				}
  				i++;
  			}
  			//if we have to move the cursor to adapt the indent
  			var indentMove = 0;
  			var wantedIndent = 0;
  			//if a margin was added (by e.g. a text-alignment), move the cursor
  			if (line[0][1]["margin-left"] !== undefined && line[0][1]["margin-left"] > 0) {
  				wantedIndent = this.pdf.internal.getCoordinateString(line[0][1]["margin-left"]);
  				indentMove = wantedIndent - currentIndent;
  				currentIndent = wantedIndent;
  			}
  			var indentMore = Math.max(blockstyle["margin-left"] || 0, 0) * fontToUnitRatio;
  			//move the cursor
  			out(indentMove + indentMore, (-1 * defaultFontSize * maxLineHeight).toFixed(2), "Td");
  			i = 0;
  			l = line.length;
  			while (i !== l) {
  				if (line[i][0]) {
  					this.RenderTextFragment(line[i][0], line[i][1]);
  				}
  				i++;
  			}
  			this.y += maxLineHeight * fontToUnitRatio;

  			//if some watcher function was executed successful, so e.g. margin and widths were changed,
  			//reset line drawing and calculate position and lines again
  			//e.g. to stop text floating around an image
  			if (this.executeWatchFunctions(line[0][1]) && lines.length > 0) {
  				var localFragments = [];
  				var localStyles = [];
  				//create fragment array of
  				lines.forEach(function (localLine) {
  					var i = 0;
  					var l = localLine.length;
  					while (i !== l) {
  						if (localLine[i][0]) {
  							localFragments.push(localLine[i][0] + ' ');
  							localStyles.push(localLine[i][1]);
  						}
  						++i;
  					}
  				});
  				//split lines again due to possible coordinate changes
  				lines = this.splitFragmentsIntoLines(PurgeWhiteSpace(localFragments), localStyles);
  				//reposition the current cursor
  				out("ET", "Q");
  				out("q", "BT 0 g", this.pdf.internal.getCoordinateString(this.x), this.pdf.internal.getVerticalCoordinateString(this.y), "Td");
  			}
  		}
  		if (cb && typeof cb === "function") {
  			cb.call(this, this.x - 9, this.y - fontSize / 2);
  		}
  		out("ET", "Q");
  		return this.y += paragraphspacing_after;
  	};
  	Renderer.prototype.setBlockBoundary = function (cb) {
  		return this.renderParagraph(cb);
  	};
  	Renderer.prototype.setBlockStyle = function (css) {
  		return this.paragraph.blockstyle = css;
  	};
  	Renderer.prototype.addText = function (text, css) {
  		this.paragraph.text.push(text);
  		return this.paragraph.style.push(css);
  	};
  	FontNameDB = {
  		helvetica: "helvetica",
  		"sans-serif": "helvetica",
  		"times new roman": "times",
  		serif: "times",
  		times: "times",
  		monospace: "courier",
  		courier: "courier"
  	};
  	FontWeightMap = {
  		100: "normal",
  		200: "normal",
  		300: "normal",
  		400: "normal",
  		500: "bold",
  		600: "bold",
  		700: "bold",
  		800: "bold",
  		900: "bold",
  		normal: "normal",
  		bold: "bold",
  		bolder: "bold",
  		lighter: "normal"
  	};
  	FontStyleMap = {
  		normal: "normal",
  		italic: "italic",
  		oblique: "italic"
  	};
  	TextAlignMap = {
  		left: "left",
  		right: "right",
  		center: "center",
  		justify: "justify"
  	};
  	FloatMap = {
  		none: 'none',
  		right: 'right',
  		left: 'left'
  	};
  	ClearMap = {
  		none: 'none',
  		both: 'both'
  	};
  	UnitedNumberMap = {
  		normal: 1
  	};
  	/**
    * Converts HTML-formatted text into formatted PDF text.
    *
    * Notes:
    * 2012-07-18
    * Plugin relies on having browser, DOM around. The HTML is pushed into dom and traversed.
    * Plugin relies on jQuery for CSS extraction.
    * Targeting HTML output from Markdown templating, which is a very simple
    * markup - div, span, em, strong, p. No br-based paragraph separation supported explicitly (but still may work.)
    * Images, tables are NOT supported.
    *
    * @public
    * @function
    * @param HTML {String or DOM Element} HTML-formatted text, or pointer to DOM element that is to be rendered into PDF.
    * @param x {Number} starting X coordinate in jsPDF instance's declared units.
    * @param y {Number} starting Y coordinate in jsPDF instance's declared units.
    * @param settings {Object} Additional / optional variables controlling parsing, rendering.
    * @returns {Object} jsPDF instance
    */
  	jsPDFAPI.fromHTML = function (HTML, x, y, settings, callback, margins) {
  		"use strict";

  		this.margins_doc = margins || {
  			top: 0,
  			bottom: 0
  		};
  		if (!settings) settings = {};
  		if (!settings.elementHandlers) settings.elementHandlers = {};

  		return process(this, HTML, isNaN(x) ? 4 : x, isNaN(y) ? 4 : y, settings, callback);
  	};
  })(jsPDF.API);

  /** ==================================================================== 
   * jsPDF JavaScript plugin
   * Copyright (c) 2013 Youssef Beddad, youssef.beddad@gmail.com
   * 
   * 
   * ====================================================================
   */

  /*global jsPDF */

  (function (jsPDFAPI) {
      'use strict';

      var jsNamesObj, jsJsObj, text;
      jsPDFAPI.addJS = function (txt) {
          text = txt;
          this.internal.events.subscribe('postPutResources', function (txt) {
              jsNamesObj = this.internal.newObject();
              this.internal.write('<< /Names [(EmbeddedJS) ' + (jsNamesObj + 1) + ' 0 R] >>', 'endobj');
              jsJsObj = this.internal.newObject();
              this.internal.write('<< /S /JavaScript /JS (', text, ') >>', 'endobj');
          });
          this.internal.events.subscribe('putCatalog', function () {
              if (jsNamesObj !== undefined && jsJsObj !== undefined) {
                  this.internal.write('/Names <</JavaScript ' + jsNamesObj + ' 0 R>>');
              }
          });
          return this;
      };
  })(jsPDF.API);

  /**
   * jsPDF Outline PlugIn
   * Copyright (c) 2014 Steven Spungin (TwelveTone LLC)  steven@twelvetone.tv
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  /**
   * Generates a PDF Outline
   */
  ;
  (function (jsPDFAPI) {
  	'use strict';

  	jsPDFAPI.events.push(['postPutResources', function () {
  		var pdf = this;
  		var rx = /^(\d+) 0 obj$/;

  		// Write action goto objects for each page
  		// this.outline.destsGoto = [];
  		// for (var i = 0; i < totalPages; i++) {
  		// var id = pdf.internal.newObject();
  		// this.outline.destsGoto.push(id);
  		// pdf.internal.write("<</D[" + (i * 2 + 3) + " 0 R /XYZ null
  		// null null]/S/GoTo>> endobj");
  		// }
  		//
  		// for (var i = 0; i < dests.length; i++) {
  		// pdf.internal.write("(page_" + (i + 1) + ")" + dests[i] + " 0
  		// R");
  		// }
  		//				
  		if (this.outline.root.children.length > 0) {
  			var lines = pdf.outline.render().split(/\r\n/);
  			for (var i = 0; i < lines.length; i++) {
  				var line = lines[i];
  				var m = rx.exec(line);
  				if (m != null) {
  					var oid = m[1];
  					pdf.internal.newObjectDeferredBegin(oid);
  				}
  				pdf.internal.write(line);
  			}
  		}

  		// This code will write named destination for each page reference
  		// (page_1, etc)
  		if (this.outline.createNamedDestinations) {
  			var totalPages = this.internal.pages.length;
  			// WARNING: this assumes jsPDF starts on page 3 and pageIDs
  			// follow 5, 7, 9, etc
  			// Write destination objects for each page
  			var dests = [];
  			for (var i = 0; i < totalPages; i++) {
  				var id = pdf.internal.newObject();
  				dests.push(id);
  				var info = pdf.internal.getPageInfo(i + 1);
  				pdf.internal.write("<< /D[" + info.objId + " 0 R /XYZ null null null]>> endobj");
  			}

  			// assign a name for each destination
  			var names2Oid = pdf.internal.newObject();
  			pdf.internal.write('<< /Names [ ');
  			for (var i = 0; i < dests.length; i++) {
  				pdf.internal.write("(page_" + (i + 1) + ")" + dests[i] + " 0 R");
  			}
  			pdf.internal.write(' ] >>', 'endobj');

  			// var kids = pdf.internal.newObject();
  			// pdf.internal.write('<< /Kids [ ' + names2Oid + ' 0 R');
  			// pdf.internal.write(' ] >>', 'endobj');

  			var namesOid = pdf.internal.newObject();
  			pdf.internal.write('<< /Dests ' + names2Oid + " 0 R");
  			pdf.internal.write('>>', 'endobj');
  		}
  	}]);

  	jsPDFAPI.events.push(['putCatalog', function () {
  		var pdf = this;
  		if (pdf.outline.root.children.length > 0) {
  			pdf.internal.write("/Outlines", this.outline.makeRef(this.outline.root));
  			if (this.outline.createNamedDestinations) {
  				pdf.internal.write("/Names " + namesOid + " 0 R");
  			}
  			// Open with Bookmarks showing
  			// pdf.internal.write("/PageMode /UseOutlines");
  		}
  	}]);

  	jsPDFAPI.events.push(['initialized', function () {
  		var pdf = this;

  		pdf.outline = {
  			createNamedDestinations: false,
  			root: {
  				children: []
  			}
  		};

  		var namesOid;
  		var destsGoto = [];

  		/**
     * Options: pageNumber
     */
  		pdf.outline.add = function (parent, title, options) {
  			var item = {
  				title: title,
  				options: options,
  				children: []
  			};
  			if (parent == null) {
  				parent = this.root;
  			}
  			parent.children.push(item);
  			return item;
  		};

  		pdf.outline.render = function () {
  			this.ctx = {};
  			this.ctx.val = '';
  			this.ctx.pdf = pdf;

  			this.genIds_r(this.root);
  			this.renderRoot(this.root);
  			this.renderItems(this.root);

  			return this.ctx.val;
  		};

  		pdf.outline.genIds_r = function (node) {
  			node.id = pdf.internal.newObjectDeferred();
  			for (var i = 0; i < node.children.length; i++) {
  				this.genIds_r(node.children[i]);
  			}
  		};

  		pdf.outline.renderRoot = function (node) {
  			this.objStart(node);
  			this.line('/Type /Outlines');
  			if (node.children.length > 0) {
  				this.line('/First ' + this.makeRef(node.children[0]));
  				this.line('/Last ' + this.makeRef(node.children[node.children.length - 1]));
  			}
  			this.line('/Count ' + this.count_r({
  				count: 0
  			}, node));
  			this.objEnd();
  		};

  		pdf.outline.renderItems = function (node) {
  			for (var i = 0; i < node.children.length; i++) {
  				var item = node.children[i];
  				this.objStart(item);

  				this.line('/Title ' + this.makeString(item.title));

  				this.line('/Parent ' + this.makeRef(node));
  				if (i > 0) {
  					this.line('/Prev ' + this.makeRef(node.children[i - 1]));
  				}
  				if (i < node.children.length - 1) {
  					this.line('/Next ' + this.makeRef(node.children[i + 1]));
  				}
  				if (item.children.length > 0) {
  					this.line('/First ' + this.makeRef(item.children[0]));
  					this.line('/Last ' + this.makeRef(item.children[item.children.length - 1]));
  				}

  				var count = this.count = this.count_r({
  					count: 0
  				}, item);
  				if (count > 0) {
  					this.line('/Count ' + count);
  				}

  				if (item.options) {
  					if (item.options.pageNumber) {
  						// Explicit Destination
  						//WARNING this assumes page ids are 3,5,7, etc.
  						var info = pdf.internal.getPageInfo(item.options.pageNumber);
  						this.line('/Dest ' + '[' + info.objId + ' 0 R /XYZ 0 ' + this.ctx.pdf.internal.pageSize.height + ' 0]');
  						// this line does not work on all clients (pageNumber instead of page ref)
  						//this.line('/Dest ' + '[' + (item.options.pageNumber - 1) + ' /XYZ 0 ' + this.ctx.pdf.internal.pageSize.height + ' 0]');

  						// Named Destination
  						// this.line('/Dest (page_' + (item.options.pageNumber) + ')');

  						// Action Destination
  						// var id = pdf.internal.newObject();
  						// pdf.internal.write('<</D[' + (item.options.pageNumber - 1) + ' /XYZ null null null]/S/GoTo>> endobj');
  						// this.line('/A ' + id + ' 0 R' );
  					}
  				}
  				this.objEnd();
  			}
  			for (var i = 0; i < node.children.length; i++) {
  				var item = node.children[i];
  				this.renderItems(item);
  			}
  		};

  		pdf.outline.line = function (text) {
  			this.ctx.val += text + '\r\n';
  		};

  		pdf.outline.makeRef = function (node) {
  			return node.id + ' 0 R';
  		};

  		pdf.outline.makeString = function (val) {
  			return '(' + pdf.internal.pdfEscape(val) + ')';
  		};

  		pdf.outline.objStart = function (node) {
  			this.ctx.val += '\r\n' + node.id + ' 0 obj' + '\r\n<<\r\n';
  		};

  		pdf.outline.objEnd = function (node) {
  			this.ctx.val += '>> \r\n' + 'endobj' + '\r\n';
  		};

  		pdf.outline.count_r = function (ctx, node) {
  			for (var i = 0; i < node.children.length; i++) {
  				ctx.count++;
  				this.count_r(ctx, node.children[i]);
  			}
  			return ctx.count;
  		};
  	}]);

  	return this;
  })(jsPDF.API);

  /**@preserve
   *  ====================================================================
   * jsPDF PNG PlugIn
   * Copyright (c) 2014 James Robb, https://github.com/jamesbrobb
   *
   * 
   * ====================================================================
   */

  (function (jsPDFAPI) {
  	'use strict';

  	/*
    * @see http://www.w3.org/TR/PNG-Chunks.html
    *
    Color    Allowed      Interpretation
    Type     Bit Depths
   	   0       1,2,4,8,16  Each pixel is a grayscale sample.
   	   2       8,16        Each pixel is an R,G,B triple.
   	   3       1,2,4,8     Each pixel is a palette index;
                          a PLTE chunk must appear.
   	   4       8,16        Each pixel is a grayscale sample,
                          followed by an alpha sample.
   	   6       8,16        Each pixel is an R,G,B triple,
                          followed by an alpha sample.
   */

  	/*
    * PNG filter method types
    *
    * @see http://www.w3.org/TR/PNG-Filters.html
    * @see http://www.libpng.org/pub/png/book/chapter09.html
    *
    * This is what the value 'Predictor' in decode params relates to
    *
    * 15 is "optimal prediction", which means the prediction algorithm can change from line to line.
    * In that case, you actually have to read the first byte off each line for the prediction algorthim (which should be 0-4, corresponding to PDF 10-14) and select the appropriate unprediction algorithm based on that byte.
    *
      0       None
      1       Sub
      2       Up
      3       Average
      4       Paeth
    */

  	var doesNotHavePngJS = function doesNotHavePngJS() {
  		return typeof PNG !== 'function' || typeof FlateStream !== 'function';
  	},
  	    canCompress = function canCompress(value) {
  		return value !== jsPDFAPI.image_compression.NONE && hasCompressionJS();
  	},
  	    hasCompressionJS = function hasCompressionJS() {
  		var inst = typeof Deflater === 'function';
  		if (!inst) throw new Error("requires deflate.js for compression");
  		return inst;
  	},
  	    compressBytes = function compressBytes(bytes, lineLength, colorsPerPixel, compression) {

  		var level = 5,
  		    filter_method = filterUp;

  		switch (compression) {

  			case jsPDFAPI.image_compression.FAST:

  				level = 3;
  				filter_method = filterSub;
  				break;

  			case jsPDFAPI.image_compression.MEDIUM:

  				level = 6;
  				filter_method = filterAverage;
  				break;

  			case jsPDFAPI.image_compression.SLOW:

  				level = 9;
  				filter_method = filterPaeth; //uses to sum to choose best filter for each line
  				break;
  		}

  		bytes = applyPngFilterMethod(bytes, lineLength, colorsPerPixel, filter_method);

  		var header = new Uint8Array(createZlibHeader(level));
  		var checksum = adler32(bytes);

  		var deflate = new Deflater(level);
  		var a = deflate.append(bytes);
  		var cBytes = deflate.flush();

  		var len = header.length + a.length + cBytes.length;

  		var cmpd = new Uint8Array(len + 4);
  		cmpd.set(header);
  		cmpd.set(a, header.length);
  		cmpd.set(cBytes, header.length + a.length);

  		cmpd[len++] = checksum >>> 24 & 0xff;
  		cmpd[len++] = checksum >>> 16 & 0xff;
  		cmpd[len++] = checksum >>> 8 & 0xff;
  		cmpd[len++] = checksum & 0xff;

  		return jsPDFAPI.arrayBufferToBinaryString(cmpd);
  	},
  	    createZlibHeader = function createZlibHeader(bytes, level) {
  		/*
     * @see http://www.ietf.org/rfc/rfc1950.txt for zlib header
     */
  		var cm = 8;
  		var cinfo = Math.LOG2E * Math.log(0x8000) - 8;
  		var cmf = cinfo << 4 | cm;

  		var hdr = cmf << 8;
  		var flevel = Math.min(3, (level - 1 & 0xff) >> 1);

  		hdr |= flevel << 6;
  		hdr |= 0; //FDICT
  		hdr += 31 - hdr % 31;

  		return [cmf, hdr & 0xff & 0xff];
  	},
  	    adler32 = function adler32(array, param) {
  		var adler = 1;
  		var s1 = adler & 0xffff,
  		    s2 = adler >>> 16 & 0xffff;
  		var len = array.length;
  		var tlen;
  		var i = 0;

  		while (len > 0) {
  			tlen = len > param ? param : len;
  			len -= tlen;
  			do {
  				s1 += array[i++];
  				s2 += s1;
  			} while (--tlen);

  			s1 %= 65521;
  			s2 %= 65521;
  		}

  		return (s2 << 16 | s1) >>> 0;
  	},
  	    applyPngFilterMethod = function applyPngFilterMethod(bytes, lineLength, colorsPerPixel, filter_method) {
  		var lines = bytes.length / lineLength,
  		    result = new Uint8Array(bytes.length + lines),
  		    filter_methods = getFilterMethods(),
  		    i = 0,
  		    line,
  		    prevLine,
  		    offset;

  		for (; i < lines; i++) {
  			offset = i * lineLength;
  			line = bytes.subarray(offset, offset + lineLength);

  			if (filter_method) {
  				result.set(filter_method(line, colorsPerPixel, prevLine), offset + i);
  			} else {

  				var j = 0,
  				    len = filter_methods.length,
  				    results = [];

  				for (; j < len; j++) {
  					results[j] = filter_methods[j](line, colorsPerPixel, prevLine);
  				}var ind = getIndexOfSmallestSum(results.concat());

  				result.set(results[ind], offset + i);
  			}

  			prevLine = line;
  		}

  		return result;
  	},
  	    filterNone = function filterNone(line, colorsPerPixel, prevLine) {
  		/*var result = new Uint8Array(line.length + 1);
    result[0] = 0;
    result.set(line, 1);*/

  		var result = Array.apply([], line);
  		result.unshift(0);

  		return result;
  	},
  	    filterSub = function filterSub(line, colorsPerPixel, prevLine) {
  		var result = [],
  		    i = 0,
  		    len = line.length,
  		    left;

  		result[0] = 1;

  		for (; i < len; i++) {
  			left = line[i - colorsPerPixel] || 0;
  			result[i + 1] = line[i] - left + 0x0100 & 0xff;
  		}

  		return result;
  	},
  	    filterUp = function filterUp(line, colorsPerPixel, prevLine) {
  		var result = [],
  		    i = 0,
  		    len = line.length,
  		    up;

  		result[0] = 2;

  		for (; i < len; i++) {
  			up = prevLine && prevLine[i] || 0;
  			result[i + 1] = line[i] - up + 0x0100 & 0xff;
  		}

  		return result;
  	},
  	    filterAverage = function filterAverage(line, colorsPerPixel, prevLine) {
  		var result = [],
  		    i = 0,
  		    len = line.length,
  		    left,
  		    up;

  		result[0] = 3;

  		for (; i < len; i++) {
  			left = line[i - colorsPerPixel] || 0;
  			up = prevLine && prevLine[i] || 0;
  			result[i + 1] = line[i] + 0x0100 - (left + up >>> 1) & 0xff;
  		}

  		return result;
  	},
  	    filterPaeth = function filterPaeth(line, colorsPerPixel, prevLine) {
  		var result = [],
  		    i = 0,
  		    len = line.length,
  		    left,
  		    up,
  		    upLeft,
  		    paeth;

  		result[0] = 4;

  		for (; i < len; i++) {
  			left = line[i - colorsPerPixel] || 0;
  			up = prevLine && prevLine[i] || 0;
  			upLeft = prevLine && prevLine[i - colorsPerPixel] || 0;
  			paeth = paethPredictor(left, up, upLeft);
  			result[i + 1] = line[i] - paeth + 0x0100 & 0xff;
  		}

  		return result;
  	},
  	    paethPredictor = function paethPredictor(left, up, upLeft) {

  		var p = left + up - upLeft,
  		    pLeft = Math.abs(p - left),
  		    pUp = Math.abs(p - up),
  		    pUpLeft = Math.abs(p - upLeft);

  		return pLeft <= pUp && pLeft <= pUpLeft ? left : pUp <= pUpLeft ? up : upLeft;
  	},
  	    getFilterMethods = function getFilterMethods() {
  		return [filterNone, filterSub, filterUp, filterAverage, filterPaeth];
  	},
  	    getIndexOfSmallestSum = function getIndexOfSmallestSum(arrays) {
  		var i = 0,
  		    len = arrays.length,
  		    sum,
  		    min,
  		    ind;

  		while (i < len) {
  			sum = absSum(arrays[i].slice(1));

  			if (sum < min || !min) {
  				min = sum;
  				ind = i;
  			}

  			i++;
  		}

  		return ind;
  	},
  	    absSum = function absSum(array) {
  		var i = 0,
  		    len = array.length,
  		    sum = 0;

  		while (i < len) {
  			sum += Math.abs(array[i++]);
  		}return sum;
  	},
  	    getPredictorFromCompression = function getPredictorFromCompression(compression) {
  		var predictor;
  		switch (compression) {
  			case jsPDFAPI.image_compression.FAST:
  				predictor = 11;
  				break;

  			case jsPDFAPI.image_compression.MEDIUM:
  				predictor = 13;
  				break;

  			case jsPDFAPI.image_compression.SLOW:
  				predictor = 14;
  				break;
  		}
  		return predictor;
  	},
  	    logImg = function logImg(img) {
  		console.log("width: " + img.width);
  		console.log("height: " + img.height);
  		console.log("bits: " + img.bits);
  		console.log("colorType: " + img.colorType);
  		console.log("transparency:");
  		console.log(img.transparency);
  		console.log("text:");
  		console.log(img.text);
  		console.log("compressionMethod: " + img.compressionMethod);
  		console.log("filterMethod: " + img.filterMethod);
  		console.log("interlaceMethod: " + img.interlaceMethod);
  		console.log("imgData:");
  		console.log(img.imgData);
  		console.log("palette:");
  		console.log(img.palette);
  		console.log("colors: " + img.colors);
  		console.log("colorSpace: " + img.colorSpace);
  		console.log("pixelBitlength: " + img.pixelBitlength);
  		console.log("hasAlphaChannel: " + img.hasAlphaChannel);
  	};

  	jsPDFAPI.processPNG = function (imageData, imageIndex, alias, compression, dataAsBinaryString) {
  		'use strict';

  		var colorSpace = this.color_spaces.DEVICE_RGB,
  		    decode = this.decode.FLATE_DECODE,
  		    bpc = 8,
  		    img,
  		    dp,
  		    trns,
  		    colors,
  		    pal,
  		    smask;

  		/*	if(this.isString(imageData)) {
    		}*/

  		if (this.isArrayBuffer(imageData)) imageData = new Uint8Array(imageData);

  		if (this.isArrayBufferView(imageData)) {

  			if (doesNotHavePngJS()) throw new Error("PNG support requires png.js and zlib.js");

  			img = new PNG(imageData);
  			imageData = img.imgData;
  			bpc = img.bits;
  			colorSpace = img.colorSpace;
  			colors = img.colors;

  			//logImg(img);

  			/*
      * colorType 6 - Each pixel is an R,G,B triple, followed by an alpha sample.
      *
      * colorType 4 - Each pixel is a grayscale sample, followed by an alpha sample.
      *
      * Extract alpha to create two separate images, using the alpha as a sMask
      */
  			if ([4, 6].indexOf(img.colorType) !== -1) {

  				/*
       * processes 8 bit RGBA and grayscale + alpha images
       */
  				if (img.bits === 8) {

  					var pixels = img.pixelBitlength == 32 ? new Uint32Array(img.decodePixels().buffer) : img.pixelBitlength == 16 ? new Uint16Array(img.decodePixels().buffer) : new Uint8Array(img.decodePixels().buffer),
  					    len = pixels.length,
  					    imgData = new Uint8Array(len * img.colors),
  					    alphaData = new Uint8Array(len),
  					    pDiff = img.pixelBitlength - img.bits,
  					    i = 0,
  					    n = 0,
  					    pixel,
  					    pbl;

  					for (; i < len; i++) {
  						pixel = pixels[i];
  						pbl = 0;

  						while (pbl < pDiff) {

  							imgData[n++] = pixel >>> pbl & 0xff;
  							pbl = pbl + img.bits;
  						}

  						alphaData[i] = pixel >>> pbl & 0xff;
  					}
  				}

  				/*
       * processes 16 bit RGBA and grayscale + alpha images
       */
  				if (img.bits === 16) {

  					var pixels = new Uint32Array(img.decodePixels().buffer),
  					    len = pixels.length,
  					    imgData = new Uint8Array(len * (32 / img.pixelBitlength) * img.colors),
  					    alphaData = new Uint8Array(len * (32 / img.pixelBitlength)),
  					    hasColors = img.colors > 1,
  					    i = 0,
  					    n = 0,
  					    a = 0,
  					    pixel;

  					while (i < len) {
  						pixel = pixels[i++];

  						imgData[n++] = pixel >>> 0 & 0xFF;

  						if (hasColors) {
  							imgData[n++] = pixel >>> 16 & 0xFF;

  							pixel = pixels[i++];
  							imgData[n++] = pixel >>> 0 & 0xFF;
  						}

  						alphaData[a++] = pixel >>> 16 & 0xFF;
  					}

  					bpc = 8;
  				}

  				if (canCompress(compression)) {

  					imageData = compressBytes(imgData, img.width * img.colors, img.colors, compression);
  					smask = compressBytes(alphaData, img.width, 1, compression);
  				} else {

  					imageData = imgData;
  					smask = alphaData;
  					decode = null;
  				}
  			}

  			/*
      * Indexed png. Each pixel is a palette index.
      */
  			if (img.colorType === 3) {

  				colorSpace = this.color_spaces.INDEXED;
  				pal = img.palette;

  				if (img.transparency.indexed) {

  					var trans = img.transparency.indexed;

  					var total = 0,
  					    i = 0,
  					    len = trans.length;

  					for (; i < len; ++i) {
  						total += trans[i];
  					}total = total / 255;

  					/*
        * a single color is specified as 100% transparent (0),
        * so we set trns to use a /Mask with that index
        */
  					if (total === len - 1 && trans.indexOf(0) !== -1) {
  						trns = [trans.indexOf(0)];

  						/*
         * there's more than one colour within the palette that specifies
         * a transparency value less than 255, so we unroll the pixels to create an image sMask
         */
  					} else if (total !== len) {

  						var pixels = img.decodePixels(),
  						    alphaData = new Uint8Array(pixels.length),
  						    i = 0,
  						    len = pixels.length;

  						for (; i < len; i++) {
  							alphaData[i] = trans[pixels[i]];
  						}smask = compressBytes(alphaData, img.width, 1);
  					}
  				}
  			}

  			var predictor = getPredictorFromCompression(compression);

  			if (decode === this.decode.FLATE_DECODE) dp = '/Predictor ' + predictor + ' /Colors ' + colors + ' /BitsPerComponent ' + bpc + ' /Columns ' + img.width;else
  				//remove 'Predictor' as it applies to the type of png filter applied to its IDAT - we only apply with compression
  				dp = '/Colors ' + colors + ' /BitsPerComponent ' + bpc + ' /Columns ' + img.width;

  			if (this.isArrayBuffer(imageData) || this.isArrayBufferView(imageData)) imageData = this.arrayBufferToBinaryString(imageData);

  			if (smask && this.isArrayBuffer(smask) || this.isArrayBufferView(smask)) smask = this.arrayBufferToBinaryString(smask);

  			return this.createImageInfo(imageData, img.width, img.height, colorSpace, bpc, decode, imageIndex, alias, dp, trns, pal, smask, predictor);
  		}

  		throw new Error("Unsupported PNG image data, try using JPEG instead.");
  	};
  })(jsPDF.API);

  /**
   * jsPDF Autoprint Plugin
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  (function (jsPDFAPI) {
    'use strict';

    jsPDFAPI.autoPrint = function () {
      'use strict';

      var refAutoPrintTag;

      this.internal.events.subscribe('postPutResources', function () {
        refAutoPrintTag = this.internal.newObject();
        this.internal.write("<< /S/Named /Type/Action /N/Print >>", "endobj");
      });

      this.internal.events.subscribe("putCatalog", function () {
        this.internal.write("/OpenAction " + refAutoPrintTag + " 0" + " R");
      });
      return this;
    };
  })(jsPDF.API);

  /** @preserve
   * jsPDF split_text_to_size plugin - MIT license.
   * Copyright (c) 2012 Willow Systems Corporation, willow-systems.com
   *               2014 Diego Casorran, https://github.com/diegocr
   */
  /**
   * 
   * ====================================================================
   */

  ;(function (API) {
  	'use strict';

  	/**
   Returns an array of length matching length of the 'word' string, with each
   cell ocupied by the width of the char in that position.
   
   @function
   @param word {String}
   @param widths {Object}
   @param kerning {Object}
   @returns {Array}
   */

  	var getCharWidthsArray = API.getCharWidthsArray = function (text, options) {

  		if (!options) {
  			options = {};
  		}

  		var widths = options.widths ? options.widths : this.internal.getFont().metadata.Unicode.widths,
  		    widthsFractionOf = widths.fof ? widths.fof : 1,
  		    kerning = options.kerning ? options.kerning : this.internal.getFont().metadata.Unicode.kerning,
  		    kerningFractionOf = kerning.fof ? kerning.fof : 1;

  		// console.log("widths, kergnings", widths, kerning)

  		var i,
  		    l,
  		    char_code,
  		    prior_char_code = 0 // for kerning
  		,
  		    default_char_width = widths[0] || widthsFractionOf,
  		    output = [];

  		for (i = 0, l = text.length; i < l; i++) {
  			char_code = text.charCodeAt(i);
  			output.push((widths[char_code] || default_char_width) / widthsFractionOf + (kerning[char_code] && kerning[char_code][prior_char_code] || 0) / kerningFractionOf);
  			prior_char_code = char_code;
  		}

  		return output;
  	};
  	var getArraySum = function getArraySum(array) {
  		var i = array.length,
  		    output = 0;
  		while (i) {
  			;i--;
  			output += array[i];
  		}
  		return output;
  	};
  	/**
   Returns a widths of string in a given font, if the font size is set as 1 point.
   
   In other words, this is "proportional" value. For 1 unit of font size, the length
   of the string will be that much.
   
   Multiply by font size to get actual width in *points*
   Then divide by 72 to get inches or divide by (72/25.6) to get 'mm' etc.
   
   @public
   @function
   @param
   @returns {Type}
   */
  	var getStringUnitWidth = API.getStringUnitWidth = function (text, options) {
  		return getArraySum(getCharWidthsArray.call(this, text, options));
  	};

  	/**
   returns array of lines
   */
  	var splitLongWord = function splitLongWord(word, widths_array, firstLineMaxLen, maxLen) {
  		var answer = [];

  		// 1st, chop off the piece that can fit on the hanging line.
  		var i = 0,
  		    l = word.length,
  		    workingLen = 0;
  		while (i !== l && workingLen + widths_array[i] < firstLineMaxLen) {
  			workingLen += widths_array[i];i++;
  		}
  		// this is first line.
  		answer.push(word.slice(0, i));

  		// 2nd. Split the rest into maxLen pieces.
  		var startOfLine = i;
  		workingLen = 0;
  		while (i !== l) {
  			if (workingLen + widths_array[i] > maxLen) {
  				answer.push(word.slice(startOfLine, i));
  				workingLen = 0;
  				startOfLine = i;
  			}
  			workingLen += widths_array[i];i++;
  		}
  		if (startOfLine !== i) {
  			answer.push(word.slice(startOfLine, i));
  		}

  		return answer;
  	};

  	// Note, all sizing inputs for this function must be in "font measurement units"
  	// By default, for PDF, it's "point".
  	var splitParagraphIntoLines = function splitParagraphIntoLines(text, maxlen, options) {
  		// at this time works only on Western scripts, ones with space char
  		// separating the words. Feel free to expand.

  		if (!options) {
  			options = {};
  		}

  		var line = [],
  		    lines = [line],
  		    line_length = options.textIndent || 0,
  		    separator_length = 0,
  		    current_word_length = 0,
  		    word,
  		    widths_array,
  		    words = text.split(' '),
  		    spaceCharWidth = getCharWidthsArray(' ', options)[0],
  		    i,
  		    l,
  		    tmp,
  		    lineIndent;

  		if (options.lineIndent === -1) {
  			lineIndent = words[0].length + 2;
  		} else {
  			lineIndent = options.lineIndent || 0;
  		}
  		if (lineIndent) {
  			var pad = Array(lineIndent).join(" "),
  			    wrds = [];
  			words.map(function (wrd) {
  				wrd = wrd.split(/\s*\n/);
  				if (wrd.length > 1) {
  					wrds = wrds.concat(wrd.map(function (wrd, idx) {
  						return (idx && wrd.length ? "\n" : "") + wrd;
  					}));
  				} else {
  					wrds.push(wrd[0]);
  				}
  			});
  			words = wrds;
  			lineIndent = getStringUnitWidth(pad, options);
  		}

  		for (i = 0, l = words.length; i < l; i++) {
  			var force = 0;

  			word = words[i];
  			if (lineIndent && word[0] == "\n") {
  				word = word.substr(1);
  				force = 1;
  			}
  			widths_array = getCharWidthsArray(word, options);
  			current_word_length = getArraySum(widths_array);

  			if (line_length + separator_length + current_word_length > maxlen || force) {
  				if (current_word_length > maxlen) {
  					// this happens when you have space-less long URLs for example.
  					// we just chop these to size. We do NOT insert hiphens
  					tmp = splitLongWord(word, widths_array, maxlen - (line_length + separator_length), maxlen);
  					// first line we add to existing line object
  					line.push(tmp.shift()); // it's ok to have extra space indicator there
  					// last line we make into new line object
  					line = [tmp.pop()];
  					// lines in the middle we apped to lines object as whole lines
  					while (tmp.length) {
  						lines.push([tmp.shift()]); // single fragment occupies whole line
  					}
  					current_word_length = getArraySum(widths_array.slice(word.length - line[0].length));
  				} else {
  					// just put it on a new line
  					line = [word];
  				}

  				// now we attach new line to lines
  				lines.push(line);
  				line_length = current_word_length + lineIndent;
  				separator_length = spaceCharWidth;
  			} else {
  				line.push(word);

  				line_length += separator_length + current_word_length;
  				separator_length = spaceCharWidth;
  			}
  		}

  		if (lineIndent) {
  			var postProcess = function postProcess(ln, idx) {
  				return (idx ? pad : '') + ln.join(" ");
  			};
  		} else {
  			var postProcess = function postProcess(ln) {
  				return ln.join(" ");
  			};
  		}

  		return lines.map(postProcess);
  	};

  	/**
   Splits a given string into an array of strings. Uses 'size' value
   (in measurement units declared as default for the jsPDF instance)
   and the font's "widths" and "Kerning" tables, where available, to
   determine display length of a given string for a given font.
   
   We use character's 100% of unit size (height) as width when Width
   table or other default width is not available.
   
   @public
   @function
   @param text {String} Unencoded, regular JavaScript (Unicode, UTF-16 / UCS-2) string.
   @param size {Number} Nominal number, measured in units default to this instance of jsPDF.
   @param options {Object} Optional flags needed for chopper to do the right thing.
   @returns {Array} with strings chopped to size.
   */
  	API.splitTextToSize = function (text, maxlen, options) {
  		'use strict';

  		if (!options) {
  			options = {};
  		}

  		var fsize = options.fontSize || this.internal.getFontSize(),
  		    newOptions = function (options) {
  			var widths = { 0: 1 },
  			    kerning = {};

  			if (!options.widths || !options.kerning) {
  				var f = this.internal.getFont(options.fontName, options.fontStyle),
  				    encoding = 'Unicode';
  				// NOT UTF8, NOT UTF16BE/LE, NOT UCS2BE/LE
  				// Actual JavaScript-native String's 16bit char codes used.
  				// no multi-byte logic here

  				if (f.metadata[encoding]) {
  					return {
  						widths: f.metadata[encoding].widths || widths,
  						kerning: f.metadata[encoding].kerning || kerning
  					};
  				}
  			} else {
  				return {
  					widths: options.widths,
  					kerning: options.kerning
  				};
  			}

  			// then use default values
  			return {
  				widths: widths,
  				kerning: kerning
  			};
  		}.call(this, options);

  		// first we split on end-of-line chars
  		var paragraphs;
  		if (Array.isArray(text)) {
  			paragraphs = text;
  		} else {
  			paragraphs = text.split(/\r?\n/);
  		}

  		// now we convert size (max length of line) into "font size units"
  		// at present time, the "font size unit" is always 'point'
  		// 'proportional' means, "in proportion to font size"
  		var fontUnit_maxLen = 1.0 * this.internal.scaleFactor * maxlen / fsize;
  		// at this time, fsize is always in "points" regardless of the default measurement unit of the doc.
  		// this may change in the future?
  		// until then, proportional_maxlen is likely to be in 'points'

  		// If first line is to be indented (shorter or longer) than maxLen
  		// we indicate that by using CSS-style "text-indent" option.
  		// here it's in font units too (which is likely 'points')
  		// it can be negative (which makes the first line longer than maxLen)
  		newOptions.textIndent = options.textIndent ? options.textIndent * 1.0 * this.internal.scaleFactor / fsize : 0;
  		newOptions.lineIndent = options.lineIndent;

  		var i,
  		    l,
  		    output = [];
  		for (i = 0, l = paragraphs.length; i < l; i++) {
  			output = output.concat(splitParagraphIntoLines(paragraphs[i], fontUnit_maxLen, newOptions));
  		}

  		return output;
  	};
  })(jsPDF.API);

  /** @preserve 
  jsPDF standard_fonts_metrics plugin
  Copyright (c) 2012 Willow Systems Corporation, willow-systems.com
  MIT license.
  */
  /**
   * 
   * ====================================================================
   */

  ;(function (API) {
  	'use strict';

  	/*
   # reference (Python) versions of 'compress' and 'uncompress'
   # only 'uncompress' function is featured lower as JavaScript
   # if you want to unit test "roundtrip", just transcribe the reference
   # 'compress' function from Python into JavaScript
   
   def compress(data):
   
   	keys =   '0123456789abcdef'
   	values = 'klmnopqrstuvwxyz'
   	mapping = dict(zip(keys, values))
   	vals = []
   	for key in data.keys():
   		value = data[key]
   		try:
   			keystring = hex(key)[2:]
   			keystring = keystring[:-1] + mapping[keystring[-1:]]
   		except:
   			keystring = key.join(["'","'"])
   			#print('Keystring is %s' % keystring)
   
   		try:
   			if value < 0:
   				valuestring = hex(value)[3:]
   				numberprefix = '-'
   			else:
   				valuestring = hex(value)[2:]
   				numberprefix = ''
   			valuestring = numberprefix + valuestring[:-1] + mapping[valuestring[-1:]]
   		except:
   			if type(value) == dict:
   				valuestring = compress(value)
   			else:
   				raise Exception("Don't know what to do with value type %s" % type(value))
   
   		vals.append(keystring+valuestring)
   	
   	return '{' + ''.join(vals) + '}'
   
   def uncompress(data):
   
   	decoded = '0123456789abcdef'
   	encoded = 'klmnopqrstuvwxyz'
   	mapping = dict(zip(encoded, decoded))
   
   	sign = +1
   	stringmode = False
   	stringparts = []
   
   	output = {}
   
   	activeobject = output
   	parentchain = []
   
   	keyparts = ''
   	valueparts = ''
   
   	key = None
   
   	ending = set(encoded)
   
   	i = 1
   	l = len(data) - 1 # stripping starting, ending {}
   	while i != l: # stripping {}
   		# -, {, }, ' are special.
   
   		ch = data[i]
   		i += 1
   
   		if ch == "'":
   			if stringmode:
   				# end of string mode
   				stringmode = False
   				key = ''.join(stringparts)
   			else:
   				# start of string mode
   				stringmode = True
   				stringparts = []
   		elif stringmode == True:
   			#print("Adding %s to stringpart" % ch)
   			stringparts.append(ch)
   
   		elif ch == '{':
   			# start of object
   			parentchain.append( [activeobject, key] )
   			activeobject = {}
   			key = None
   			#DEBUG = True
   		elif ch == '}':
   			# end of object
   			parent, key = parentchain.pop()
   			parent[key] = activeobject
   			key = None
   			activeobject = parent
   			#DEBUG = False
   
   		elif ch == '-':
   			sign = -1
   		else:
   			# must be number
   			if key == None:
   				#debug("In Key. It is '%s', ch is '%s'" % (keyparts, ch))
   				if ch in ending:
   					#debug("End of key")
   					keyparts += mapping[ch]
   					key = int(keyparts, 16) * sign
   					sign = +1
   					keyparts = ''
   				else:
   					keyparts += ch
   			else:
   				#debug("In value. It is '%s', ch is '%s'" % (valueparts, ch))
   				if ch in ending:
   					#debug("End of value")
   					valueparts += mapping[ch]
   					activeobject[key] = int(valueparts, 16) * sign
   					sign = +1
   					key = None
   					valueparts = ''
   				else:
   					valueparts += ch
   
   			#debug(activeobject)
   
   	return output
   
   */

  	/**
   Uncompresses data compressed into custom, base16-like format. 
   @public
   @function
   @param
   @returns {Type}
   */

  	var uncompress = function uncompress(data) {

  		var decoded = '0123456789abcdef',
  		    encoded = 'klmnopqrstuvwxyz',
  		    mapping = {};

  		for (var i = 0; i < encoded.length; i++) {
  			mapping[encoded[i]] = decoded[i];
  		}

  		var undef,
  		    output = {},
  		    sign = 1,
  		    stringparts // undef. will be [] in string mode

  		,
  		    activeobject = output,
  		    parentchain = [],
  		    parent_key_pair,
  		    keyparts = '',
  		    valueparts = '',
  		    key // undef. will be Truthy when Key is resolved.
  		,
  		    datalen = data.length - 1 // stripping ending }
  		,
  		    ch;

  		i = 1; // stripping starting {

  		while (i != datalen) {
  			// - { } ' are special.

  			ch = data[i];
  			i += 1;

  			if (ch == "'") {
  				if (stringparts) {
  					// end of string mode
  					key = stringparts.join('');
  					stringparts = undef;
  				} else {
  					// start of string mode
  					stringparts = [];
  				}
  			} else if (stringparts) {
  				stringparts.push(ch);
  			} else if (ch == '{') {
  				// start of object
  				parentchain.push([activeobject, key]);
  				activeobject = {};
  				key = undef;
  			} else if (ch == '}') {
  				// end of object
  				parent_key_pair = parentchain.pop();
  				parent_key_pair[0][parent_key_pair[1]] = activeobject;
  				key = undef;
  				activeobject = parent_key_pair[0];
  			} else if (ch == '-') {
  				sign = -1;
  			} else {
  				// must be number
  				if (key === undef) {
  					if (mapping.hasOwnProperty(ch)) {
  						keyparts += mapping[ch];
  						key = parseInt(keyparts, 16) * sign;
  						sign = +1;
  						keyparts = '';
  					} else {
  						keyparts += ch;
  					}
  				} else {
  					if (mapping.hasOwnProperty(ch)) {
  						valueparts += mapping[ch];
  						activeobject[key] = parseInt(valueparts, 16) * sign;
  						sign = +1;
  						key = undef;
  						valueparts = '';
  					} else {
  						valueparts += ch;
  					}
  				}
  			}
  		} // end while

  		return output;
  	};

  	// encoding = 'Unicode' 
  	// NOT UTF8, NOT UTF16BE/LE, NOT UCS2BE/LE. NO clever BOM behavior
  	// Actual 16bit char codes used.
  	// no multi-byte logic here

  	// Unicode characters to WinAnsiEncoding:
  	// {402: 131, 8211: 150, 8212: 151, 8216: 145, 8217: 146, 8218: 130, 8220: 147, 8221: 148, 8222: 132, 8224: 134, 8225: 135, 8226: 149, 8230: 133, 8364: 128, 8240:137, 8249: 139, 8250: 155, 710: 136, 8482: 153, 338: 140, 339: 156, 732: 152, 352: 138, 353: 154, 376: 159, 381: 142, 382: 158}
  	// as you can see, all Unicode chars are outside of 0-255 range. No char code conflicts.
  	// this means that you can give Win cp1252 encoded strings to jsPDF for rendering directly
  	// as well as give strings with some (supported by these fonts) Unicode characters and 
  	// these will be mapped to win cp1252 
  	// for example, you can send char code (cp1252) 0x80 or (unicode) 0x20AC, getting "Euro" glyph displayed in both cases.

  	var encodingBlock = {
  		'codePages': ['WinAnsiEncoding'],
  		'WinAnsiEncoding': uncompress("{19m8n201n9q201o9r201s9l201t9m201u8m201w9n201x9o201y8o202k8q202l8r202m9p202q8p20aw8k203k8t203t8v203u9v2cq8s212m9t15m8w15n9w2dw9s16k8u16l9u17s9z17x8y17y9y}")
  	},
  	    encodings = { 'Unicode': {
  			'Courier': encodingBlock,
  			'Courier-Bold': encodingBlock,
  			'Courier-BoldOblique': encodingBlock,
  			'Courier-Oblique': encodingBlock,
  			'Helvetica': encodingBlock,
  			'Helvetica-Bold': encodingBlock,
  			'Helvetica-BoldOblique': encodingBlock,
  			'Helvetica-Oblique': encodingBlock,
  			'Times-Roman': encodingBlock,
  			'Times-Bold': encodingBlock,
  			'Times-BoldItalic': encodingBlock,
  			'Times-Italic': encodingBlock
  			//	, 'Symbol'
  			//	, 'ZapfDingbats'
  		} }
  	/** 
   Resources:
   Font metrics data is reprocessed derivative of contents of
   "Font Metrics for PDF Core 14 Fonts" package, which exhibits the following copyright and license:
   
   Copyright (c) 1989, 1990, 1991, 1992, 1993, 1997 Adobe Systems Incorporated. All Rights Reserved.
   
   This file and the 14 PostScript(R) AFM files it accompanies may be used,
   copied, and distributed for any purpose and without charge, with or without
   modification, provided that all copyright notices are retained; that the AFM
   files are not distributed without this file; that all modifications to this
   file or any of the AFM files are prominently noted in the modified file(s);
   and that this paragraph is not modified. Adobe Systems has no responsibility
   or obligation to support the use of the AFM files.
   
   */
  	,
  	    fontMetrics = { 'Unicode': {
  			// all sizing numbers are n/fontMetricsFractionOf = one font size unit
  			// this means that if fontMetricsFractionOf = 1000, and letter A's width is 476, it's
  			// width is 476/1000 or 47.6% of its height (regardless of font size)
  			// At this time this value applies to "widths" and "kerning" numbers.

  			// char code 0 represents "default" (average) width - use it for chars missing in this table.
  			// key 'fof' represents the "fontMetricsFractionOf" value

  			'Courier-Oblique': uncompress("{'widths'{k3w'fof'6o}'kerning'{'fof'-6o}}"),
  			'Times-BoldItalic': uncompress("{'widths'{k3o2q4ycx2r201n3m201o6o201s2l201t2l201u2l201w3m201x3m201y3m2k1t2l2r202m2n2n3m2o3m2p5n202q6o2r1w2s2l2t2l2u3m2v3t2w1t2x2l2y1t2z1w3k3m3l3m3m3m3n3m3o3m3p3m3q3m3r3m3s3m203t2l203u2l3v2l3w3t3x3t3y3t3z3m4k5n4l4m4m4m4n4m4o4s4p4m4q4m4r4s4s4y4t2r4u3m4v4m4w3x4x5t4y4s4z4s5k3x5l4s5m4m5n3r5o3x5p4s5q4m5r5t5s4m5t3x5u3x5v2l5w1w5x2l5y3t5z3m6k2l6l3m6m3m6n2w6o3m6p2w6q2l6r3m6s3r6t1w6u1w6v3m6w1w6x4y6y3r6z3m7k3m7l3m7m2r7n2r7o1w7p3r7q2w7r4m7s3m7t2w7u2r7v2n7w1q7x2n7y3t202l3mcl4mal2ram3man3mao3map3mar3mas2lat4uau1uav3maw3way4uaz2lbk2sbl3t'fof'6obo2lbp3tbq3mbr1tbs2lbu1ybv3mbz3mck4m202k3mcm4mcn4mco4mcp4mcq5ycr4mcs4mct4mcu4mcv4mcw2r2m3rcy2rcz2rdl4sdm4sdn4sdo4sdp4sdq4sds4sdt4sdu4sdv4sdw4sdz3mek3mel3mem3men3meo3mep3meq4ser2wes2wet2weu2wev2wew1wex1wey1wez1wfl3rfm3mfn3mfo3mfp3mfq3mfr3tfs3mft3rfu3rfv3rfw3rfz2w203k6o212m6o2dw2l2cq2l3t3m3u2l17s3x19m3m}'kerning'{cl{4qu5kt5qt5rs17ss5ts}201s{201ss}201t{cks4lscmscnscoscpscls2wu2yu201ts}201x{2wu2yu}2k{201ts}2w{4qx5kx5ou5qx5rs17su5tu}2x{17su5tu5ou}2y{4qx5kx5ou5qx5rs17ss5ts}'fof'-6ofn{17sw5tw5ou5qw5rs}7t{cksclscmscnscoscps4ls}3u{17su5tu5os5qs}3v{17su5tu5os5qs}7p{17su5tu}ck{4qu5kt5qt5rs17ss5ts}4l{4qu5kt5qt5rs17ss5ts}cm{4qu5kt5qt5rs17ss5ts}cn{4qu5kt5qt5rs17ss5ts}co{4qu5kt5qt5rs17ss5ts}cp{4qu5kt5qt5rs17ss5ts}6l{4qu5ou5qw5rt17su5tu}5q{ckuclucmucnucoucpu4lu}5r{ckuclucmucnucoucpu4lu}7q{cksclscmscnscoscps4ls}6p{4qu5ou5qw5rt17sw5tw}ek{4qu5ou5qw5rt17su5tu}el{4qu5ou5qw5rt17su5tu}em{4qu5ou5qw5rt17su5tu}en{4qu5ou5qw5rt17su5tu}eo{4qu5ou5qw5rt17su5tu}ep{4qu5ou5qw5rt17su5tu}es{17ss5ts5qs4qu}et{4qu5ou5qw5rt17sw5tw}eu{4qu5ou5qw5rt17ss5ts}ev{17ss5ts5qs4qu}6z{17sw5tw5ou5qw5rs}fm{17sw5tw5ou5qw5rs}7n{201ts}fo{17sw5tw5ou5qw5rs}fp{17sw5tw5ou5qw5rs}fq{17sw5tw5ou5qw5rs}7r{cksclscmscnscoscps4ls}fs{17sw5tw5ou5qw5rs}ft{17su5tu}fu{17su5tu}fv{17su5tu}fw{17su5tu}fz{cksclscmscnscoscps4ls}}}"),
  			'Helvetica-Bold': uncompress("{'widths'{k3s2q4scx1w201n3r201o6o201s1w201t1w201u1w201w3m201x3m201y3m2k1w2l2l202m2n2n3r2o3r2p5t202q6o2r1s2s2l2t2l2u2r2v3u2w1w2x2l2y1w2z1w3k3r3l3r3m3r3n3r3o3r3p3r3q3r3r3r3s3r203t2l203u2l3v2l3w3u3x3u3y3u3z3x4k6l4l4s4m4s4n4s4o4s4p4m4q3x4r4y4s4s4t1w4u3r4v4s4w3x4x5n4y4s4z4y5k4m5l4y5m4s5n4m5o3x5p4s5q4m5r5y5s4m5t4m5u3x5v2l5w1w5x2l5y3u5z3r6k2l6l3r6m3x6n3r6o3x6p3r6q2l6r3x6s3x6t1w6u1w6v3r6w1w6x5t6y3x6z3x7k3x7l3x7m2r7n3r7o2l7p3x7q3r7r4y7s3r7t3r7u3m7v2r7w1w7x2r7y3u202l3rcl4sal2lam3ran3rao3rap3rar3ras2lat4tau2pav3raw3uay4taz2lbk2sbl3u'fof'6obo2lbp3xbq3rbr1wbs2lbu2obv3rbz3xck4s202k3rcm4scn4sco4scp4scq6ocr4scs4mct4mcu4mcv4mcw1w2m2zcy1wcz1wdl4sdm4ydn4ydo4ydp4ydq4yds4ydt4sdu4sdv4sdw4sdz3xek3rel3rem3ren3reo3rep3req5ter3res3ret3reu3rev3rew1wex1wey1wez1wfl3xfm3xfn3xfo3xfp3xfq3xfr3ufs3xft3xfu3xfv3xfw3xfz3r203k6o212m6o2dw2l2cq2l3t3r3u2l17s4m19m3r}'kerning'{cl{4qs5ku5ot5qs17sv5tv}201t{2ww4wy2yw}201w{2ks}201x{2ww4wy2yw}2k{201ts201xs}2w{7qs4qu5kw5os5qw5rs17su5tu7tsfzs}2x{5ow5qs}2y{7qs4qu5kw5os5qw5rs17su5tu7tsfzs}'fof'-6o7p{17su5tu5ot}ck{4qs5ku5ot5qs17sv5tv}4l{4qs5ku5ot5qs17sv5tv}cm{4qs5ku5ot5qs17sv5tv}cn{4qs5ku5ot5qs17sv5tv}co{4qs5ku5ot5qs17sv5tv}cp{4qs5ku5ot5qs17sv5tv}6l{17st5tt5os}17s{2kwclvcmvcnvcovcpv4lv4wwckv}5o{2kucltcmtcntcotcpt4lt4wtckt}5q{2ksclscmscnscoscps4ls4wvcks}5r{2ks4ws}5t{2kwclvcmvcnvcovcpv4lv4wwckv}eo{17st5tt5os}fu{17su5tu5ot}6p{17ss5ts}ek{17st5tt5os}el{17st5tt5os}em{17st5tt5os}en{17st5tt5os}6o{201ts}ep{17st5tt5os}es{17ss5ts}et{17ss5ts}eu{17ss5ts}ev{17ss5ts}6z{17su5tu5os5qt}fm{17su5tu5os5qt}fn{17su5tu5os5qt}fo{17su5tu5os5qt}fp{17su5tu5os5qt}fq{17su5tu5os5qt}fs{17su5tu5os5qt}ft{17su5tu5ot}7m{5os}fv{17su5tu5ot}fw{17su5tu5ot}}}"),
  			'Courier': uncompress("{'widths'{k3w'fof'6o}'kerning'{'fof'-6o}}"),
  			'Courier-BoldOblique': uncompress("{'widths'{k3w'fof'6o}'kerning'{'fof'-6o}}"),
  			'Times-Bold': uncompress("{'widths'{k3q2q5ncx2r201n3m201o6o201s2l201t2l201u2l201w3m201x3m201y3m2k1t2l2l202m2n2n3m2o3m2p6o202q6o2r1w2s2l2t2l2u3m2v3t2w1t2x2l2y1t2z1w3k3m3l3m3m3m3n3m3o3m3p3m3q3m3r3m3s3m203t2l203u2l3v2l3w3t3x3t3y3t3z3m4k5x4l4s4m4m4n4s4o4s4p4m4q3x4r4y4s4y4t2r4u3m4v4y4w4m4x5y4y4s4z4y5k3x5l4y5m4s5n3r5o4m5p4s5q4s5r6o5s4s5t4s5u4m5v2l5w1w5x2l5y3u5z3m6k2l6l3m6m3r6n2w6o3r6p2w6q2l6r3m6s3r6t1w6u2l6v3r6w1w6x5n6y3r6z3m7k3r7l3r7m2w7n2r7o2l7p3r7q3m7r4s7s3m7t3m7u2w7v2r7w1q7x2r7y3o202l3mcl4sal2lam3man3mao3map3mar3mas2lat4uau1yav3maw3tay4uaz2lbk2sbl3t'fof'6obo2lbp3rbr1tbs2lbu2lbv3mbz3mck4s202k3mcm4scn4sco4scp4scq6ocr4scs4mct4mcu4mcv4mcw2r2m3rcy2rcz2rdl4sdm4ydn4ydo4ydp4ydq4yds4ydt4sdu4sdv4sdw4sdz3rek3mel3mem3men3meo3mep3meq4ser2wes2wet2weu2wev2wew1wex1wey1wez1wfl3rfm3mfn3mfo3mfp3mfq3mfr3tfs3mft3rfu3rfv3rfw3rfz3m203k6o212m6o2dw2l2cq2l3t3m3u2l17s4s19m3m}'kerning'{cl{4qt5ks5ot5qy5rw17sv5tv}201t{cks4lscmscnscoscpscls4wv}2k{201ts}2w{4qu5ku7mu5os5qx5ru17su5tu}2x{17su5tu5ou5qs}2y{4qv5kv7mu5ot5qz5ru17su5tu}'fof'-6o7t{cksclscmscnscoscps4ls}3u{17su5tu5os5qu}3v{17su5tu5os5qu}fu{17su5tu5ou5qu}7p{17su5tu5ou5qu}ck{4qt5ks5ot5qy5rw17sv5tv}4l{4qt5ks5ot5qy5rw17sv5tv}cm{4qt5ks5ot5qy5rw17sv5tv}cn{4qt5ks5ot5qy5rw17sv5tv}co{4qt5ks5ot5qy5rw17sv5tv}cp{4qt5ks5ot5qy5rw17sv5tv}6l{17st5tt5ou5qu}17s{ckuclucmucnucoucpu4lu4wu}5o{ckuclucmucnucoucpu4lu4wu}5q{ckzclzcmzcnzcozcpz4lz4wu}5r{ckxclxcmxcnxcoxcpx4lx4wu}5t{ckuclucmucnucoucpu4lu4wu}7q{ckuclucmucnucoucpu4lu}6p{17sw5tw5ou5qu}ek{17st5tt5qu}el{17st5tt5ou5qu}em{17st5tt5qu}en{17st5tt5qu}eo{17st5tt5qu}ep{17st5tt5ou5qu}es{17ss5ts5qu}et{17sw5tw5ou5qu}eu{17sw5tw5ou5qu}ev{17ss5ts5qu}6z{17sw5tw5ou5qu5rs}fm{17sw5tw5ou5qu5rs}fn{17sw5tw5ou5qu5rs}fo{17sw5tw5ou5qu5rs}fp{17sw5tw5ou5qu5rs}fq{17sw5tw5ou5qu5rs}7r{cktcltcmtcntcotcpt4lt5os}fs{17sw5tw5ou5qu5rs}ft{17su5tu5ou5qu}7m{5os}fv{17su5tu5ou5qu}fw{17su5tu5ou5qu}fz{cksclscmscnscoscps4ls}}}")
  			//, 'Symbol': uncompress("{'widths'{k3uaw4r19m3m2k1t2l2l202m2y2n3m2p5n202q6o3k3m2s2l2t2l2v3r2w1t3m3m2y1t2z1wbk2sbl3r'fof'6o3n3m3o3m3p3m3q3m3r3m3s3m3t3m3u1w3v1w3w3r3x3r3y3r3z2wbp3t3l3m5v2l5x2l5z3m2q4yfr3r7v3k7w1o7x3k}'kerning'{'fof'-6o}}")
  			, 'Helvetica': uncompress("{'widths'{k3p2q4mcx1w201n3r201o6o201s1q201t1q201u1q201w2l201x2l201y2l2k1w2l1w202m2n2n3r2o3r2p5t202q6o2r1n2s2l2t2l2u2r2v3u2w1w2x2l2y1w2z1w3k3r3l3r3m3r3n3r3o3r3p3r3q3r3r3r3s3r203t2l203u2l3v1w3w3u3x3u3y3u3z3r4k6p4l4m4m4m4n4s4o4s4p4m4q3x4r4y4s4s4t1w4u3m4v4m4w3r4x5n4y4s4z4y5k4m5l4y5m4s5n4m5o3x5p4s5q4m5r5y5s4m5t4m5u3x5v1w5w1w5x1w5y2z5z3r6k2l6l3r6m3r6n3m6o3r6p3r6q1w6r3r6s3r6t1q6u1q6v3m6w1q6x5n6y3r6z3r7k3r7l3r7m2l7n3m7o1w7p3r7q3m7r4s7s3m7t3m7u3m7v2l7w1u7x2l7y3u202l3rcl4mal2lam3ran3rao3rap3rar3ras2lat4tau2pav3raw3uay4taz2lbk2sbl3u'fof'6obo2lbp3rbr1wbs2lbu2obv3rbz3xck4m202k3rcm4mcn4mco4mcp4mcq6ocr4scs4mct4mcu4mcv4mcw1w2m2ncy1wcz1wdl4sdm4ydn4ydo4ydp4ydq4yds4ydt4sdu4sdv4sdw4sdz3xek3rel3rem3ren3reo3rep3req5ter3mes3ret3reu3rev3rew1wex1wey1wez1wfl3rfm3rfn3rfo3rfp3rfq3rfr3ufs3xft3rfu3rfv3rfw3rfz3m203k6o212m6o2dw2l2cq2l3t3r3u1w17s4m19m3r}'kerning'{5q{4wv}cl{4qs5kw5ow5qs17sv5tv}201t{2wu4w1k2yu}201x{2wu4wy2yu}17s{2ktclucmucnu4otcpu4lu4wycoucku}2w{7qs4qz5k1m17sy5ow5qx5rsfsu5ty7tufzu}2x{17sy5ty5oy5qs}2y{7qs4qz5k1m17sy5ow5qx5rsfsu5ty7tufzu}'fof'-6o7p{17sv5tv5ow}ck{4qs5kw5ow5qs17sv5tv}4l{4qs5kw5ow5qs17sv5tv}cm{4qs5kw5ow5qs17sv5tv}cn{4qs5kw5ow5qs17sv5tv}co{4qs5kw5ow5qs17sv5tv}cp{4qs5kw5ow5qs17sv5tv}6l{17sy5ty5ow}do{17st5tt}4z{17st5tt}7s{fst}dm{17st5tt}dn{17st5tt}5o{ckwclwcmwcnwcowcpw4lw4wv}dp{17st5tt}dq{17st5tt}7t{5ow}ds{17st5tt}5t{2ktclucmucnu4otcpu4lu4wycoucku}fu{17sv5tv5ow}6p{17sy5ty5ow5qs}ek{17sy5ty5ow}el{17sy5ty5ow}em{17sy5ty5ow}en{5ty}eo{17sy5ty5ow}ep{17sy5ty5ow}es{17sy5ty5qs}et{17sy5ty5ow5qs}eu{17sy5ty5ow5qs}ev{17sy5ty5ow5qs}6z{17sy5ty5ow5qs}fm{17sy5ty5ow5qs}fn{17sy5ty5ow5qs}fo{17sy5ty5ow5qs}fp{17sy5ty5qs}fq{17sy5ty5ow5qs}7r{5ow}fs{17sy5ty5ow5qs}ft{17sv5tv5ow}7m{5ow}fv{17sv5tv5ow}fw{17sv5tv5ow}}}"),
  			'Helvetica-BoldOblique': uncompress("{'widths'{k3s2q4scx1w201n3r201o6o201s1w201t1w201u1w201w3m201x3m201y3m2k1w2l2l202m2n2n3r2o3r2p5t202q6o2r1s2s2l2t2l2u2r2v3u2w1w2x2l2y1w2z1w3k3r3l3r3m3r3n3r3o3r3p3r3q3r3r3r3s3r203t2l203u2l3v2l3w3u3x3u3y3u3z3x4k6l4l4s4m4s4n4s4o4s4p4m4q3x4r4y4s4s4t1w4u3r4v4s4w3x4x5n4y4s4z4y5k4m5l4y5m4s5n4m5o3x5p4s5q4m5r5y5s4m5t4m5u3x5v2l5w1w5x2l5y3u5z3r6k2l6l3r6m3x6n3r6o3x6p3r6q2l6r3x6s3x6t1w6u1w6v3r6w1w6x5t6y3x6z3x7k3x7l3x7m2r7n3r7o2l7p3x7q3r7r4y7s3r7t3r7u3m7v2r7w1w7x2r7y3u202l3rcl4sal2lam3ran3rao3rap3rar3ras2lat4tau2pav3raw3uay4taz2lbk2sbl3u'fof'6obo2lbp3xbq3rbr1wbs2lbu2obv3rbz3xck4s202k3rcm4scn4sco4scp4scq6ocr4scs4mct4mcu4mcv4mcw1w2m2zcy1wcz1wdl4sdm4ydn4ydo4ydp4ydq4yds4ydt4sdu4sdv4sdw4sdz3xek3rel3rem3ren3reo3rep3req5ter3res3ret3reu3rev3rew1wex1wey1wez1wfl3xfm3xfn3xfo3xfp3xfq3xfr3ufs3xft3xfu3xfv3xfw3xfz3r203k6o212m6o2dw2l2cq2l3t3r3u2l17s4m19m3r}'kerning'{cl{4qs5ku5ot5qs17sv5tv}201t{2ww4wy2yw}201w{2ks}201x{2ww4wy2yw}2k{201ts201xs}2w{7qs4qu5kw5os5qw5rs17su5tu7tsfzs}2x{5ow5qs}2y{7qs4qu5kw5os5qw5rs17su5tu7tsfzs}'fof'-6o7p{17su5tu5ot}ck{4qs5ku5ot5qs17sv5tv}4l{4qs5ku5ot5qs17sv5tv}cm{4qs5ku5ot5qs17sv5tv}cn{4qs5ku5ot5qs17sv5tv}co{4qs5ku5ot5qs17sv5tv}cp{4qs5ku5ot5qs17sv5tv}6l{17st5tt5os}17s{2kwclvcmvcnvcovcpv4lv4wwckv}5o{2kucltcmtcntcotcpt4lt4wtckt}5q{2ksclscmscnscoscps4ls4wvcks}5r{2ks4ws}5t{2kwclvcmvcnvcovcpv4lv4wwckv}eo{17st5tt5os}fu{17su5tu5ot}6p{17ss5ts}ek{17st5tt5os}el{17st5tt5os}em{17st5tt5os}en{17st5tt5os}6o{201ts}ep{17st5tt5os}es{17ss5ts}et{17ss5ts}eu{17ss5ts}ev{17ss5ts}6z{17su5tu5os5qt}fm{17su5tu5os5qt}fn{17su5tu5os5qt}fo{17su5tu5os5qt}fp{17su5tu5os5qt}fq{17su5tu5os5qt}fs{17su5tu5os5qt}ft{17su5tu5ot}7m{5os}fv{17su5tu5ot}fw{17su5tu5ot}}}")
  			//, 'ZapfDingbats': uncompress("{'widths'{k4u2k1w'fof'6o}'kerning'{'fof'-6o}}")
  			, 'Courier-Bold': uncompress("{'widths'{k3w'fof'6o}'kerning'{'fof'-6o}}"),
  			'Times-Italic': uncompress("{'widths'{k3n2q4ycx2l201n3m201o5t201s2l201t2l201u2l201w3r201x3r201y3r2k1t2l2l202m2n2n3m2o3m2p5n202q5t2r1p2s2l2t2l2u3m2v4n2w1t2x2l2y1t2z1w3k3m3l3m3m3m3n3m3o3m3p3m3q3m3r3m3s3m203t2l203u2l3v2l3w4n3x4n3y4n3z3m4k5w4l3x4m3x4n4m4o4s4p3x4q3x4r4s4s4s4t2l4u2w4v4m4w3r4x5n4y4m4z4s5k3x5l4s5m3x5n3m5o3r5p4s5q3x5r5n5s3x5t3r5u3r5v2r5w1w5x2r5y2u5z3m6k2l6l3m6m3m6n2w6o3m6p2w6q1w6r3m6s3m6t1w6u1w6v2w6w1w6x4s6y3m6z3m7k3m7l3m7m2r7n2r7o1w7p3m7q2w7r4m7s2w7t2w7u2r7v2s7w1v7x2s7y3q202l3mcl3xal2ram3man3mao3map3mar3mas2lat4wau1vav3maw4nay4waz2lbk2sbl4n'fof'6obo2lbp3mbq3obr1tbs2lbu1zbv3mbz3mck3x202k3mcm3xcn3xco3xcp3xcq5tcr4mcs3xct3xcu3xcv3xcw2l2m2ucy2lcz2ldl4mdm4sdn4sdo4sdp4sdq4sds4sdt4sdu4sdv4sdw4sdz3mek3mel3mem3men3meo3mep3meq4mer2wes2wet2weu2wev2wew1wex1wey1wez1wfl3mfm3mfn3mfo3mfp3mfq3mfr4nfs3mft3mfu3mfv3mfw3mfz2w203k6o212m6m2dw2l2cq2l3t3m3u2l17s3r19m3m}'kerning'{cl{5kt4qw}201s{201sw}201t{201tw2wy2yy6q-t}201x{2wy2yy}2k{201tw}2w{7qs4qy7rs5ky7mw5os5qx5ru17su5tu}2x{17ss5ts5os}2y{7qs4qy7rs5ky7mw5os5qx5ru17su5tu}'fof'-6o6t{17ss5ts5qs}7t{5os}3v{5qs}7p{17su5tu5qs}ck{5kt4qw}4l{5kt4qw}cm{5kt4qw}cn{5kt4qw}co{5kt4qw}cp{5kt4qw}6l{4qs5ks5ou5qw5ru17su5tu}17s{2ks}5q{ckvclvcmvcnvcovcpv4lv}5r{ckuclucmucnucoucpu4lu}5t{2ks}6p{4qs5ks5ou5qw5ru17su5tu}ek{4qs5ks5ou5qw5ru17su5tu}el{4qs5ks5ou5qw5ru17su5tu}em{4qs5ks5ou5qw5ru17su5tu}en{4qs5ks5ou5qw5ru17su5tu}eo{4qs5ks5ou5qw5ru17su5tu}ep{4qs5ks5ou5qw5ru17su5tu}es{5ks5qs4qs}et{4qs5ks5ou5qw5ru17su5tu}eu{4qs5ks5qw5ru17su5tu}ev{5ks5qs4qs}ex{17ss5ts5qs}6z{4qv5ks5ou5qw5ru17su5tu}fm{4qv5ks5ou5qw5ru17su5tu}fn{4qv5ks5ou5qw5ru17su5tu}fo{4qv5ks5ou5qw5ru17su5tu}fp{4qv5ks5ou5qw5ru17su5tu}fq{4qv5ks5ou5qw5ru17su5tu}7r{5os}fs{4qv5ks5ou5qw5ru17su5tu}ft{17su5tu5qs}fu{17su5tu5qs}fv{17su5tu5qs}fw{17su5tu5qs}}}"),
  			'Times-Roman': uncompress("{'widths'{k3n2q4ycx2l201n3m201o6o201s2l201t2l201u2l201w2w201x2w201y2w2k1t2l2l202m2n2n3m2o3m2p5n202q6o2r1m2s2l2t2l2u3m2v3s2w1t2x2l2y1t2z1w3k3m3l3m3m3m3n3m3o3m3p3m3q3m3r3m3s3m203t2l203u2l3v1w3w3s3x3s3y3s3z2w4k5w4l4s4m4m4n4m4o4s4p3x4q3r4r4s4s4s4t2l4u2r4v4s4w3x4x5t4y4s4z4s5k3r5l4s5m4m5n3r5o3x5p4s5q4s5r5y5s4s5t4s5u3x5v2l5w1w5x2l5y2z5z3m6k2l6l2w6m3m6n2w6o3m6p2w6q2l6r3m6s3m6t1w6u1w6v3m6w1w6x4y6y3m6z3m7k3m7l3m7m2l7n2r7o1w7p3m7q3m7r4s7s3m7t3m7u2w7v3k7w1o7x3k7y3q202l3mcl4sal2lam3man3mao3map3mar3mas2lat4wau1vav3maw3say4waz2lbk2sbl3s'fof'6obo2lbp3mbq2xbr1tbs2lbu1zbv3mbz2wck4s202k3mcm4scn4sco4scp4scq5tcr4mcs3xct3xcu3xcv3xcw2l2m2tcy2lcz2ldl4sdm4sdn4sdo4sdp4sdq4sds4sdt4sdu4sdv4sdw4sdz3mek2wel2wem2wen2weo2wep2weq4mer2wes2wet2weu2wev2wew1wex1wey1wez1wfl3mfm3mfn3mfo3mfp3mfq3mfr3sfs3mft3mfu3mfv3mfw3mfz3m203k6o212m6m2dw2l2cq2l3t3m3u1w17s4s19m3m}'kerning'{cl{4qs5ku17sw5ou5qy5rw201ss5tw201ws}201s{201ss}201t{ckw4lwcmwcnwcowcpwclw4wu201ts}2k{201ts}2w{4qs5kw5os5qx5ru17sx5tx}2x{17sw5tw5ou5qu}2y{4qs5kw5os5qx5ru17sx5tx}'fof'-6o7t{ckuclucmucnucoucpu4lu5os5rs}3u{17su5tu5qs}3v{17su5tu5qs}7p{17sw5tw5qs}ck{4qs5ku17sw5ou5qy5rw201ss5tw201ws}4l{4qs5ku17sw5ou5qy5rw201ss5tw201ws}cm{4qs5ku17sw5ou5qy5rw201ss5tw201ws}cn{4qs5ku17sw5ou5qy5rw201ss5tw201ws}co{4qs5ku17sw5ou5qy5rw201ss5tw201ws}cp{4qs5ku17sw5ou5qy5rw201ss5tw201ws}6l{17su5tu5os5qw5rs}17s{2ktclvcmvcnvcovcpv4lv4wuckv}5o{ckwclwcmwcnwcowcpw4lw4wu}5q{ckyclycmycnycoycpy4ly4wu5ms}5r{cktcltcmtcntcotcpt4lt4ws}5t{2ktclvcmvcnvcovcpv4lv4wuckv}7q{cksclscmscnscoscps4ls}6p{17su5tu5qw5rs}ek{5qs5rs}el{17su5tu5os5qw5rs}em{17su5tu5os5qs5rs}en{17su5qs5rs}eo{5qs5rs}ep{17su5tu5os5qw5rs}es{5qs}et{17su5tu5qw5rs}eu{17su5tu5qs5rs}ev{5qs}6z{17sv5tv5os5qx5rs}fm{5os5qt5rs}fn{17sv5tv5os5qx5rs}fo{17sv5tv5os5qx5rs}fp{5os5qt5rs}fq{5os5qt5rs}7r{ckuclucmucnucoucpu4lu5os}fs{17sv5tv5os5qx5rs}ft{17ss5ts5qs}fu{17sw5tw5qs}fv{17sw5tw5qs}fw{17ss5ts5qs}fz{ckuclucmucnucoucpu4lu5os5rs}}}"),
  			'Helvetica-Oblique': uncompress("{'widths'{k3p2q4mcx1w201n3r201o6o201s1q201t1q201u1q201w2l201x2l201y2l2k1w2l1w202m2n2n3r2o3r2p5t202q6o2r1n2s2l2t2l2u2r2v3u2w1w2x2l2y1w2z1w3k3r3l3r3m3r3n3r3o3r3p3r3q3r3r3r3s3r203t2l203u2l3v1w3w3u3x3u3y3u3z3r4k6p4l4m4m4m4n4s4o4s4p4m4q3x4r4y4s4s4t1w4u3m4v4m4w3r4x5n4y4s4z4y5k4m5l4y5m4s5n4m5o3x5p4s5q4m5r5y5s4m5t4m5u3x5v1w5w1w5x1w5y2z5z3r6k2l6l3r6m3r6n3m6o3r6p3r6q1w6r3r6s3r6t1q6u1q6v3m6w1q6x5n6y3r6z3r7k3r7l3r7m2l7n3m7o1w7p3r7q3m7r4s7s3m7t3m7u3m7v2l7w1u7x2l7y3u202l3rcl4mal2lam3ran3rao3rap3rar3ras2lat4tau2pav3raw3uay4taz2lbk2sbl3u'fof'6obo2lbp3rbr1wbs2lbu2obv3rbz3xck4m202k3rcm4mcn4mco4mcp4mcq6ocr4scs4mct4mcu4mcv4mcw1w2m2ncy1wcz1wdl4sdm4ydn4ydo4ydp4ydq4yds4ydt4sdu4sdv4sdw4sdz3xek3rel3rem3ren3reo3rep3req5ter3mes3ret3reu3rev3rew1wex1wey1wez1wfl3rfm3rfn3rfo3rfp3rfq3rfr3ufs3xft3rfu3rfv3rfw3rfz3m203k6o212m6o2dw2l2cq2l3t3r3u1w17s4m19m3r}'kerning'{5q{4wv}cl{4qs5kw5ow5qs17sv5tv}201t{2wu4w1k2yu}201x{2wu4wy2yu}17s{2ktclucmucnu4otcpu4lu4wycoucku}2w{7qs4qz5k1m17sy5ow5qx5rsfsu5ty7tufzu}2x{17sy5ty5oy5qs}2y{7qs4qz5k1m17sy5ow5qx5rsfsu5ty7tufzu}'fof'-6o7p{17sv5tv5ow}ck{4qs5kw5ow5qs17sv5tv}4l{4qs5kw5ow5qs17sv5tv}cm{4qs5kw5ow5qs17sv5tv}cn{4qs5kw5ow5qs17sv5tv}co{4qs5kw5ow5qs17sv5tv}cp{4qs5kw5ow5qs17sv5tv}6l{17sy5ty5ow}do{17st5tt}4z{17st5tt}7s{fst}dm{17st5tt}dn{17st5tt}5o{ckwclwcmwcnwcowcpw4lw4wv}dp{17st5tt}dq{17st5tt}7t{5ow}ds{17st5tt}5t{2ktclucmucnu4otcpu4lu4wycoucku}fu{17sv5tv5ow}6p{17sy5ty5ow5qs}ek{17sy5ty5ow}el{17sy5ty5ow}em{17sy5ty5ow}en{5ty}eo{17sy5ty5ow}ep{17sy5ty5ow}es{17sy5ty5qs}et{17sy5ty5ow5qs}eu{17sy5ty5ow5qs}ev{17sy5ty5ow5qs}6z{17sy5ty5ow5qs}fm{17sy5ty5ow5qs}fn{17sy5ty5ow5qs}fo{17sy5ty5ow5qs}fp{17sy5ty5qs}fq{17sy5ty5ow5qs}7r{5ow}fs{17sy5ty5ow5qs}ft{17sv5tv5ow}7m{5ow}fv{17sv5tv5ow}fw{17sv5tv5ow}}}")
  		} };

  	/*
   This event handler is fired when a new jsPDF object is initialized
   This event handler appends metrics data to standard fonts within
   that jsPDF instance. The metrics are mapped over Unicode character
   codes, NOT CIDs or other codes matching the StandardEncoding table of the
   standard PDF fonts.
   Future:
   Also included is the encoding maping table, converting Unicode (UCS-2, UTF-16)
   char codes to StandardEncoding character codes. The encoding table is to be used
   somewhere around "pdfEscape" call.
   */

  	API.events.push(['addFont', function (font) {
  		var metrics,
  		    unicode_section,
  		    encoding = 'Unicode',
  		    encodingBlock;

  		metrics = fontMetrics[encoding][font.PostScriptName];
  		if (metrics) {
  			if (font.metadata[encoding]) {
  				unicode_section = font.metadata[encoding];
  			} else {
  				unicode_section = font.metadata[encoding] = {};
  			}

  			unicode_section.widths = metrics.widths;
  			unicode_section.kerning = metrics.kerning;
  		}

  		encodingBlock = encodings[encoding][font.PostScriptName];
  		if (encodingBlock) {
  			if (font.metadata[encoding]) {
  				unicode_section = font.metadata[encoding];
  			} else {
  				unicode_section = font.metadata[encoding] = {};
  			}

  			unicode_section.encoding = encodingBlock;
  			if (encodingBlock.codePages && encodingBlock.codePages.length) {
  				font.encoding = encodingBlock.codePages[0];
  			}
  		}
  	}]); // end of adding event handler
  })(jsPDF.API);

  /** @preserve
  jsPDF SVG plugin
  Copyright (c) 2012 Willow Systems Corporation, willow-systems.com
  */
  /**
   * 
   * ====================================================================
   */

  ;(function (jsPDFAPI) {
  	'use strict';

  	/**
   Parses SVG XML and converts only some of the SVG elements into
   PDF elements.
   
   Supports:
    paths
   
   @public
   @function
   @param
   @returns {Type}
   */

  	jsPDFAPI.addSVG = function (svgtext, x, y, w, h) {
  		// 'this' is _jsPDF object returned when jsPDF is inited (new jsPDF())

  		var undef;

  		if (x === undef || y === undef) {
  			throw new Error("addSVG needs values for 'x' and 'y'");
  		}

  		function InjectCSS(cssbody, document) {
  			var styletag = document.createElement('style');
  			styletag.type = 'text/css';
  			if (styletag.styleSheet) {
  				// ie
  				styletag.styleSheet.cssText = cssbody;
  			} else {
  				// others
  				styletag.appendChild(document.createTextNode(cssbody));
  			}
  			document.getElementsByTagName("head")[0].appendChild(styletag);
  		}

  		function createWorkerNode(document) {

  			var frameID = 'childframe' // Date.now().toString() + '_' + (Math.random() * 100).toString()
  			,
  			    frame = document.createElement('iframe');

  			InjectCSS('.jsPDF_sillysvg_iframe {display:none;position:absolute;}', document);

  			frame.name = frameID;
  			frame.setAttribute("width", 0);
  			frame.setAttribute("height", 0);
  			frame.setAttribute("frameborder", "0");
  			frame.setAttribute("scrolling", "no");
  			frame.setAttribute("seamless", "seamless");
  			frame.setAttribute("class", "jsPDF_sillysvg_iframe");

  			document.body.appendChild(frame);

  			return frame;
  		}

  		function attachSVGToWorkerNode(svgtext, frame) {
  			var framedoc = (frame.contentWindow || frame.contentDocument).document;
  			framedoc.write(svgtext);
  			framedoc.close();
  			return framedoc.getElementsByTagName('svg')[0];
  		}

  		function convertPathToPDFLinesArgs(path) {
  			'use strict';
  			// we will use 'lines' method call. it needs:
  			// - starting coordinate pair
  			// - array of arrays of vector shifts (2-len for line, 6 len for bezier)
  			// - scale array [horizontal, vertical] ratios
  			// - style (stroke, fill, both)

  			var x = parseFloat(path[1]),
  			    y = parseFloat(path[2]),
  			    vectors = [],
  			    position = 3,
  			    len = path.length;

  			while (position < len) {
  				if (path[position] === 'c') {
  					vectors.push([parseFloat(path[position + 1]), parseFloat(path[position + 2]), parseFloat(path[position + 3]), parseFloat(path[position + 4]), parseFloat(path[position + 5]), parseFloat(path[position + 6])]);
  					position += 7;
  				} else if (path[position] === 'l') {
  					vectors.push([parseFloat(path[position + 1]), parseFloat(path[position + 2])]);
  					position += 3;
  				} else {
  					position += 1;
  				}
  			}
  			return [x, y, vectors];
  		}

  		var workernode = createWorkerNode(document),
  		    svgnode = attachSVGToWorkerNode(svgtext, workernode),
  		    scale = [1, 1],
  		    svgw = parseFloat(svgnode.getAttribute('width')),
  		    svgh = parseFloat(svgnode.getAttribute('height'));

  		if (svgw && svgh) {
  			// setting both w and h makes image stretch to size.
  			// this may distort the image, but fits your demanded size
  			if (w && h) {
  				scale = [w / svgw, h / svgh];
  			}
  			// if only one is set, that value is set as max and SVG
  			// is scaled proportionately.
  			else if (w) {
  					scale = [w / svgw, w / svgw];
  				} else if (h) {
  					scale = [h / svgh, h / svgh];
  				}
  		}

  		var i,
  		    l,
  		    tmp,
  		    linesargs,
  		    items = svgnode.childNodes;
  		for (i = 0, l = items.length; i < l; i++) {
  			tmp = items[i];
  			if (tmp.tagName && tmp.tagName.toUpperCase() === 'PATH') {
  				linesargs = convertPathToPDFLinesArgs(tmp.getAttribute("d").split(' '));
  				// path start x coordinate
  				linesargs[0] = linesargs[0] * scale[0] + x; // where x is upper left X of image
  				// path start y coordinate
  				linesargs[1] = linesargs[1] * scale[1] + y; // where y is upper left Y of image
  				// the rest of lines are vectors. these will adjust with scale value auto.
  				this.lines.call(this, linesargs[2] // lines
  				, linesargs[0] // starting x
  				, linesargs[1] // starting y
  				, scale);
  			}
  		}

  		// clean up
  		// workernode.parentNode.removeChild(workernode)

  		return this;
  	};
  })(jsPDF.API);

  /** ==================================================================== 
   * jsPDF total_pages plugin
   * Copyright (c) 2013 Eduardo Menezes de Morais, eduardo.morais@usp.br
   * 
   * 
   * ====================================================================
   */

  (function (jsPDFAPI) {
    'use strict';

    jsPDFAPI.putTotalPages = function (pageExpression) {
      'use strict';

      var replaceExpression = new RegExp(pageExpression, 'g');
      for (var n = 1; n <= this.internal.getNumberOfPages(); n++) {
        for (var i = 0; i < this.internal.pages[n].length; i++) {
          this.internal.pages[n][i] = this.internal.pages[n][i].replace(replaceExpression, this.internal.getNumberOfPages());
        }
      }
      return this;
    };
  })(jsPDF.API);

  /** ==================================================================== 
   * jsPDF XMP metadata plugin
   * Copyright (c) 2016 Jussi Utunen, u-jussi@suomi24.fi
   * 
   * 
   * ====================================================================
   */

  /*global jsPDF */

  /**
  * Adds XMP formatted metadata to PDF
  *
  * @param {String} metadata The actual metadata to be added. The metadata shall be stored as XMP simple value. Note that if the metadata string contains XML markup characters "<", ">" or "&", those characters should be written using XML entities.
  * @param {String} namespaceuri Sets the namespace URI for the metadata. Last character should be slash or hash.
  * @function
  * @returns {jsPDF}
  * @methodOf jsPDF#
  * @name addMetadata
  */

  (function (jsPDFAPI) {
      'use strict';

      var xmpmetadata = "";
      var xmpnamespaceuri = "";
      var metadata_object_number = "";

      jsPDFAPI.addMetadata = function (metadata, namespaceuri) {
          xmpnamespaceuri = namespaceuri || "http://jspdf.default.namespaceuri/"; //The namespace URI for an XMP name shall not be empty
          xmpmetadata = metadata;
          this.internal.events.subscribe('postPutResources', function () {
              if (!xmpmetadata) {
                  metadata_object_number = "";
              } else {
                  var xmpmeta_beginning = '<x:xmpmeta xmlns:x="adobe:ns:meta/">';
                  var rdf_beginning = '<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#"><rdf:Description rdf:about="" xmlns:jspdf="' + xmpnamespaceuri + '"><jspdf:metadata>';
                  var rdf_ending = '</jspdf:metadata></rdf:Description></rdf:RDF>';
                  var xmpmeta_ending = '</x:xmpmeta>';
                  var utf8_xmpmeta_beginning = unescape(encodeURIComponent(xmpmeta_beginning));
                  var utf8_rdf_beginning = unescape(encodeURIComponent(rdf_beginning));
                  var utf8_metadata = unescape(encodeURIComponent(xmpmetadata));
                  var utf8_rdf_ending = unescape(encodeURIComponent(rdf_ending));
                  var utf8_xmpmeta_ending = unescape(encodeURIComponent(xmpmeta_ending));

                  var total_len = utf8_rdf_beginning.length + utf8_metadata.length + utf8_rdf_ending.length + utf8_xmpmeta_beginning.length + utf8_xmpmeta_ending.length;

                  metadata_object_number = this.internal.newObject();
                  this.internal.write('<< /Type /Metadata /Subtype /XML /Length ' + total_len + ' >>');
                  this.internal.write('stream');
                  this.internal.write(utf8_xmpmeta_beginning + utf8_rdf_beginning + utf8_metadata + utf8_rdf_ending + utf8_xmpmeta_ending);
                  this.internal.write('endstream');
                  this.internal.write('endobj');
              }
          });
          this.internal.events.subscribe('putCatalog', function () {
              if (metadata_object_number) {
                  this.internal.write('/Metadata ' + metadata_object_number + ' 0 R');
              }
          });
          return this;
      };
  })(jsPDF.API);

  /* Blob.js
   * A Blob implementation.
   * 2014-07-24
   *
   * By Eli Grey, http://eligrey.com
   * By Devin Samarin, https://github.com/dsamarin
   * License: X11/MIT
   *   See https://github.com/eligrey/Blob.js/blob/master/LICENSE.md
   */

  /*global self, unescape */
  /*jslint bitwise: true, regexp: true, confusion: true, es5: true, vars: true, white: true,
    plusplus: true */

  /*! @source http://purl.eligrey.com/github/Blob.js/blob/master/Blob.js */

  (function (view) {
  	"use strict";

  	view.URL = view.URL || view.webkitURL;

  	if (view.Blob && view.URL) {
  		try {
  			new Blob;
  			return;
  		} catch (e) {}
  	}

  	// Internally we use a BlobBuilder implementation to base Blob off of
  	// in order to support older browsers that only have BlobBuilder
  	var BlobBuilder = view.BlobBuilder || view.WebKitBlobBuilder || view.MozBlobBuilder || (function(view) {
  		var
  			  get_class = function(object) {
  				return Object.prototype.toString.call(object).match(/^\[object\s(.*)\]$/)[1];
  			}
  			, FakeBlobBuilder = function BlobBuilder() {
  				this.data = [];
  			}
  			, FakeBlob = function Blob(data, type, encoding) {
  				this.data = data;
  				this.size = data.length;
  				this.type = type;
  				this.encoding = encoding;
  			}
  			, FBB_proto = FakeBlobBuilder.prototype
  			, FB_proto = FakeBlob.prototype
  			, FileReaderSync = view.FileReaderSync
  			, FileException = function(type) {
  				this.code = this[this.name = type];
  			}
  			, file_ex_codes = (
  				  "NOT_FOUND_ERR SECURITY_ERR ABORT_ERR NOT_READABLE_ERR ENCODING_ERR "
  				+ "NO_MODIFICATION_ALLOWED_ERR INVALID_STATE_ERR SYNTAX_ERR"
  			).split(" ")
  			, file_ex_code = file_ex_codes.length
  			, real_URL = view.URL || view.webkitURL || view
  			, real_create_object_URL = real_URL.createObjectURL
  			, real_revoke_object_URL = real_URL.revokeObjectURL
  			, URL = real_URL
  			, btoa = view.btoa
  			, atob = view.atob

  			, ArrayBuffer = view.ArrayBuffer
  			, Uint8Array = view.Uint8Array

  			, origin = /^[\w-]+:\/*\[?[\w\.:-]+\]?(?::[0-9]+)?/
  		;
  		FakeBlob.fake = FB_proto.fake = true;
  		while (file_ex_code--) {
  			FileException.prototype[file_ex_codes[file_ex_code]] = file_ex_code + 1;
  		}
  		// Polyfill URL
  		if (!real_URL.createObjectURL) {
  			URL = view.URL = function(uri) {
  				var
  					  uri_info = document.createElementNS("http://www.w3.org/1999/xhtml", "a")
  					, uri_origin
  				;
  				uri_info.href = uri;
  				if (!("origin" in uri_info)) {
  					if (uri_info.protocol.toLowerCase() === "data:") {
  						uri_info.origin = null;
  					} else {
  						uri_origin = uri.match(origin);
  						uri_info.origin = uri_origin && uri_origin[1];
  					}
  				}
  				return uri_info;
  			};
  		}
  		URL.createObjectURL = function(blob) {
  			var
  				  type = blob.type
  				, data_URI_header
  			;
  			if (type === null) {
  				type = "application/octet-stream";
  			}
  			if (blob instanceof FakeBlob) {
  				data_URI_header = "data:" + type;
  				if (blob.encoding === "base64") {
  					return data_URI_header + ";base64," + blob.data;
  				} else if (blob.encoding === "URI") {
  					return data_URI_header + "," + decodeURIComponent(blob.data);
  				} if (btoa) {
  					return data_URI_header + ";base64," + btoa(blob.data);
  				} else {
  					return data_URI_header + "," + encodeURIComponent(blob.data);
  				}
  			} else if (real_create_object_URL) {
  				return real_create_object_URL.call(real_URL, blob);
  			}
  		};
  		URL.revokeObjectURL = function(object_URL) {
  			if (object_URL.substring(0, 5) !== "data:" && real_revoke_object_URL) {
  				real_revoke_object_URL.call(real_URL, object_URL);
  			}
  		};
  		FBB_proto.append = function(data/*, endings*/) {
  			var bb = this.data;
  			// decode data to a binary string
  			if (Uint8Array && (data instanceof ArrayBuffer || data instanceof Uint8Array)) {
  				var
  					  str = ""
  					, buf = new Uint8Array(data)
  					, i = 0
  					, buf_len = buf.length
  				;
  				for (; i < buf_len; i++) {
  					str += String.fromCharCode(buf[i]);
  				}
  				bb.push(str);
  			} else if (get_class(data) === "Blob" || get_class(data) === "File") {
  				if (FileReaderSync) {
  					var fr = new FileReaderSync;
  					bb.push(fr.readAsBinaryString(data));
  				} else {
  					// async FileReader won't work as BlobBuilder is sync
  					throw new FileException("NOT_READABLE_ERR");
  				}
  			} else if (data instanceof FakeBlob) {
  				if (data.encoding === "base64" && atob) {
  					bb.push(atob(data.data));
  				} else if (data.encoding === "URI") {
  					bb.push(decodeURIComponent(data.data));
  				} else if (data.encoding === "raw") {
  					bb.push(data.data);
  				}
  			} else {
  				if (typeof data !== "string") {
  					data += ""; // convert unsupported types to strings
  				}
  				// decode UTF-16 to binary string
  				bb.push(unescape(encodeURIComponent(data)));
  			}
  		};
  		FBB_proto.getBlob = function(type) {
  			if (!arguments.length) {
  				type = null;
  			}
  			return new FakeBlob(this.data.join(""), type, "raw");
  		};
  		FBB_proto.toString = function() {
  			return "[object BlobBuilder]";
  		};
  		FB_proto.slice = function(start, end, type) {
  			var args = arguments.length;
  			if (args < 3) {
  				type = null;
  			}
  			return new FakeBlob(
  				  this.data.slice(start, args > 1 ? end : this.data.length)
  				, type
  				, this.encoding
  			);
  		};
  		FB_proto.toString = function() {
  			return "[object Blob]";
  		};
  		FB_proto.close = function() {
  			this.size = 0;
  			delete this.data;
  		};
  		return FakeBlobBuilder;
  	}(view));

  	view.Blob = function(blobParts, options) {
  		var type = options ? (options.type || "") : "";
  		var builder = new BlobBuilder();
  		if (blobParts) {
  			for (var i = 0, len = blobParts.length; i < len; i++) {
  				if (Uint8Array && blobParts[i] instanceof Uint8Array) {
  					builder.append(blobParts[i].buffer);
  				}
  				else {
  					builder.append(blobParts[i]);
  				}
  			}
  		}
  		var blob = builder.getBlob(type);
  		if (!blob.slice && blob.webkitSlice) {
  			blob.slice = blob.webkitSlice;
  		}
  		return blob;
  	};

  	var getPrototypeOf = Object.getPrototypeOf || function(object) {
  		return object.__proto__;
  	};
  	view.Blob.prototype = getPrototypeOf(new view.Blob());
  }(typeof self !== "undefined" && self || typeof window !== "undefined" && window || undefined.content || undefined));

  /* FileSaver.js
   * A saveAs() FileSaver implementation.
   * 1.1.20151003
   *
   * By Eli Grey, http://eligrey.com
   * License: MIT
   *   See https://github.com/eligrey/FileSaver.js/blob/master/LICENSE.md
   */

  /*global self */
  /*jslint bitwise: true, indent: 4, laxbreak: true, laxcomma: true, smarttabs: true, plusplus: true */

  /*! @source http://purl.eligrey.com/github/FileSaver.js/blob/master/FileSaver.js */

  var saveAs = saveAs || (function(view) {
  	"use strict";
  	// IE <10 is explicitly unsupported
  	if (typeof navigator !== "undefined" && /MSIE [1-9]\./.test(navigator.userAgent)) {
  		return;
  	}
  	var
  		  doc = view.document
  		  // only get URL when necessary in case Blob.js hasn't overridden it yet
  		, get_URL = function() {
  			return view.URL || view.webkitURL || view;
  		}
  		, save_link = doc.createElementNS("http://www.w3.org/1999/xhtml", "a")
  		, can_use_save_link = "download" in save_link
  		, click = function(node) {
  			var event = new MouseEvent("click");
  			node.dispatchEvent(event);
  		}
  		, is_safari = /Version\/[\d\.]+.*Safari/.test(navigator.userAgent)
  		, webkit_req_fs = view.webkitRequestFileSystem
  		, req_fs = view.requestFileSystem || webkit_req_fs || view.mozRequestFileSystem
  		, throw_outside = function(ex) {
  			(view.setImmediate || view.setTimeout)(function() {
  				throw ex;
  			}, 0);
  		}
  		, force_saveable_type = "application/octet-stream"
  		, fs_min_size = 0
  		// See https://code.google.com/p/chromium/issues/detail?id=375297#c7 and
  		// https://github.com/eligrey/FileSaver.js/commit/485930a#commitcomment-8768047
  		// for the reasoning behind the timeout and revocation flow
  		, arbitrary_revoke_timeout = 500 // in ms
  		, revoke = function(file) {
  			var revoker = function() {
  				if (typeof file === "string") { // file is an object URL
  					get_URL().revokeObjectURL(file);
  				} else { // file is a File
  					file.remove();
  				}
  			};
  			if (view.chrome) {
  				revoker();
  			} else {
  				setTimeout(revoker, arbitrary_revoke_timeout);
  			}
  		}
  		, dispatch = function(filesaver, event_types, event) {
  			event_types = [].concat(event_types);
  			var i = event_types.length;
  			while (i--) {
  				var listener = filesaver["on" + event_types[i]];
  				if (typeof listener === "function") {
  					try {
  						listener.call(filesaver, event || filesaver);
  					} catch (ex) {
  						throw_outside(ex);
  					}
  				}
  			}
  		}
  		, auto_bom = function(blob) {
  			// prepend BOM for UTF-8 XML and text/* types (including HTML)
  			if (/^\s*(?:text\/\S*|application\/xml|\S*\/\S*\+xml)\s*;.*charset\s*=\s*utf-8/i.test(blob.type)) {
  				return new Blob(["\ufeff", blob], {type: blob.type});
  			}
  			return blob;
  		}
  		, FileSaver = function(blob, name, no_auto_bom) {
  			if (!no_auto_bom) {
  				blob = auto_bom(blob);
  			}
  			// First try a.download, then web filesystem, then object URLs
  			var
  				  filesaver = this
  				, type = blob.type
  				, blob_changed = false
  				, object_url
  				, target_view
  				, dispatch_all = function() {
  					dispatch(filesaver, "writestart progress write writeend".split(" "));
  				}
  				// on any filesys errors revert to saving with object URLs
  				, fs_error = function() {
  					if (target_view && is_safari && typeof FileReader !== "undefined") {
  						// Safari doesn't allow downloading of blob urls
  						var reader = new FileReader();
  						reader.onloadend = function() {
  							var base64Data = reader.result;
  							target_view.location.href = "data:attachment/file" + base64Data.slice(base64Data.search(/[,;]/));
  							filesaver.readyState = filesaver.DONE;
  							dispatch_all();
  						};
  						reader.readAsDataURL(blob);
  						filesaver.readyState = filesaver.INIT;
  						return;
  					}
  					// don't create more object URLs than needed
  					if (blob_changed || !object_url) {
  						object_url = get_URL().createObjectURL(blob);
  					}
  					if (target_view) {
  						target_view.location.href = object_url;
  					} else {
  						var new_tab = view.open(object_url, "_blank");
  						if (new_tab == undefined && is_safari) {
  							//Apple do not allow window.open, see http://bit.ly/1kZffRI
  							view.location.href = object_url
  						}
  					}
  					filesaver.readyState = filesaver.DONE;
  					dispatch_all();
  					revoke(object_url);
  				}
  				, abortable = function(func) {
  					return function() {
  						if (filesaver.readyState !== filesaver.DONE) {
  							return func.apply(this, arguments);
  						}
  					};
  				}
  				, create_if_not_found = {create: true, exclusive: false}
  				, slice
  			;
  			filesaver.readyState = filesaver.INIT;
  			if (!name) {
  				name = "download";
  			}
  			if (can_use_save_link) {
  				object_url = get_URL().createObjectURL(blob);
  				setTimeout(function() {
  					save_link.href = object_url;
  					save_link.download = name;
  					click(save_link);
  					dispatch_all();
  					revoke(object_url);
  					filesaver.readyState = filesaver.DONE;
  				});
  				return;
  			}
  			// Object and web filesystem URLs have a problem saving in Google Chrome when
  			// viewed in a tab, so I force save with application/octet-stream
  			// http://code.google.com/p/chromium/issues/detail?id=91158
  			// Update: Google errantly closed 91158, I submitted it again:
  			// https://code.google.com/p/chromium/issues/detail?id=389642
  			if (view.chrome && type && type !== force_saveable_type) {
  				slice = blob.slice || blob.webkitSlice;
  				blob = slice.call(blob, 0, blob.size, force_saveable_type);
  				blob_changed = true;
  			}
  			// Since I can't be sure that the guessed media type will trigger a download
  			// in WebKit, I append .download to the filename.
  			// https://bugs.webkit.org/show_bug.cgi?id=65440
  			if (webkit_req_fs && name !== "download") {
  				name += ".download";
  			}
  			if (type === force_saveable_type || webkit_req_fs) {
  				target_view = view;
  			}
  			if (!req_fs) {
  				fs_error();
  				return;
  			}
  			fs_min_size += blob.size;
  			req_fs(view.TEMPORARY, fs_min_size, abortable(function(fs) {
  				fs.root.getDirectory("saved", create_if_not_found, abortable(function(dir) {
  					var save = function() {
  						dir.getFile(name, create_if_not_found, abortable(function(file) {
  							file.createWriter(abortable(function(writer) {
  								writer.onwriteend = function(event) {
  									target_view.location.href = file.toURL();
  									filesaver.readyState = filesaver.DONE;
  									dispatch(filesaver, "writeend", event);
  									revoke(file);
  								};
  								writer.onerror = function() {
  									var error = writer.error;
  									if (error.code !== error.ABORT_ERR) {
  										fs_error();
  									}
  								};
  								"writestart progress write abort".split(" ").forEach(function(event) {
  									writer["on" + event] = filesaver["on" + event];
  								});
  								writer.write(blob);
  								filesaver.abort = function() {
  									writer.abort();
  									filesaver.readyState = filesaver.DONE;
  								};
  								filesaver.readyState = filesaver.WRITING;
  							}), fs_error);
  						}), fs_error);
  					};
  					dir.getFile(name, {create: false}, abortable(function(file) {
  						// delete file if it already exists
  						file.remove();
  						save();
  					}), abortable(function(ex) {
  						if (ex.code === ex.NOT_FOUND_ERR) {
  							save();
  						} else {
  							fs_error();
  						}
  					}));
  				}), fs_error);
  			}), fs_error);
  		}
  		, FS_proto = FileSaver.prototype
  		, saveAs = function(blob, name, no_auto_bom) {
  			return new FileSaver(blob, name, no_auto_bom);
  		}
  	;
  	// IE 10+ (native saveAs)
  	if (typeof navigator !== "undefined" && navigator.msSaveOrOpenBlob) {
  		return function(blob, name, no_auto_bom) {
  			if (!no_auto_bom) {
  				blob = auto_bom(blob);
  			}
  			return navigator.msSaveOrOpenBlob(blob, name || "download");
  		};
  	}

  	FS_proto.abort = function() {
  		var filesaver = this;
  		filesaver.readyState = filesaver.DONE;
  		dispatch(filesaver, "abort");
  	};
  	FS_proto.readyState = FS_proto.INIT = 0;
  	FS_proto.WRITING = 1;
  	FS_proto.DONE = 2;

  	FS_proto.error =
  	FS_proto.onwritestart =
  	FS_proto.onprogress =
  	FS_proto.onwrite =
  	FS_proto.onabort =
  	FS_proto.onerror =
  	FS_proto.onwriteend =
  		null;

  	return saveAs;
  }(
  	   typeof self !== "undefined" && self
  	|| typeof window !== "undefined" && window
  	|| undefined.content
  ));
  // `self` is undefined in Firefox for Android content script context
  // while `this` is nsIContentFrameMessageManager
  // with an attribute `content` that corresponds to the window

  if (typeof module !== "undefined" && module.exports) {
    module.exports.saveAs = saveAs;
  } else if ((typeof define !== "undefined" && define !== null) && (define.amd != null)) {
    define([], function() {
      return saveAs;
    });
  }

  /*
   * Copyright (c) 2012 chick307 <chick307@gmail.com>
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  void function(global, callback) {
  	if (typeof module === 'object') {
  		module.exports = callback();
  	} else if (typeof define === 'function') {
  		define(callback);
  	} else {
  		global.adler32cs = callback();
  	}
  }(jsPDF, function() {
  	var _hasArrayBuffer = typeof ArrayBuffer === 'function' &&
  		typeof Uint8Array === 'function';

  	var _Buffer = null, _isBuffer = (function() {
  		if (!_hasArrayBuffer)
  			return function _isBuffer() { return false };

  		try {
  			var buffer = {};
  			if (typeof buffer.Buffer === 'function')
  				_Buffer = buffer.Buffer;
  		} catch (error) {}

  		return function _isBuffer(value) {
  			return value instanceof ArrayBuffer ||
  				_Buffer !== null && value instanceof _Buffer;
  		};
  	}());

  	var _utf8ToBinary = (function() {
  		if (_Buffer !== null) {
  			return function _utf8ToBinary(utf8String) {
  				return new _Buffer(utf8String, 'utf8').toString('binary');
  			};
  		} else {
  			return function _utf8ToBinary(utf8String) {
  				return unescape(encodeURIComponent(utf8String));
  			};
  		}
  	}());

  	var MOD = 65521;

  	var _update = function _update(checksum, binaryString) {
  		var a = checksum & 0xFFFF, b = checksum >>> 16;
  		for (var i = 0, length = binaryString.length; i < length; i++) {
  			a = (a + (binaryString.charCodeAt(i) & 0xFF)) % MOD;
  			b = (b + a) % MOD;
  		}
  		return (b << 16 | a) >>> 0;
  	};

  	var _updateUint8Array = function _updateUint8Array(checksum, uint8Array) {
  		var a = checksum & 0xFFFF, b = checksum >>> 16;
  		for (var i = 0, length = uint8Array.length, x; i < length; i++) {
  			a = (a + uint8Array[i]) % MOD;
  			b = (b + a) % MOD;
  		}
  		return (b << 16 | a) >>> 0
  	};

  	var exports = {};

  	var Adler32 = exports.Adler32 = (function() {
  		var ctor = function Adler32(checksum) {
  			if (!(this instanceof ctor)) {
  				throw new TypeError(
  					'Constructor cannot called be as a function.');
  			}
  			if (!isFinite(checksum = checksum == null ? 1 : +checksum)) {
  				throw new Error(
  					'First arguments needs to be a finite number.');
  			}
  			this.checksum = checksum >>> 0;
  		};

  		var proto = ctor.prototype = {};
  		proto.constructor = ctor;

  		ctor.from = function(from) {
  			from.prototype = proto;
  			return from;
  		}(function from(binaryString) {
  			if (!(this instanceof ctor)) {
  				throw new TypeError(
  					'Constructor cannot called be as a function.');
  			}
  			if (binaryString == null)
  				throw new Error('First argument needs to be a string.');
  			this.checksum = _update(1, binaryString.toString());
  		});

  		ctor.fromUtf8 = function(fromUtf8) {
  			fromUtf8.prototype = proto;
  			return fromUtf8;
  		}(function fromUtf8(utf8String) {
  			if (!(this instanceof ctor)) {
  				throw new TypeError(
  					'Constructor cannot called be as a function.');
  			}
  			if (utf8String == null)
  				throw new Error('First argument needs to be a string.');
  			var binaryString = _utf8ToBinary(utf8String.toString());
  			this.checksum = _update(1, binaryString);
  		});

  		if (_hasArrayBuffer) {
  			ctor.fromBuffer = function(fromBuffer) {
  				fromBuffer.prototype = proto;
  				return fromBuffer;
  			}(function fromBuffer(buffer) {
  				if (!(this instanceof ctor)) {
  					throw new TypeError(
  						'Constructor cannot called be as a function.');
  				}
  				if (!_isBuffer(buffer))
  					throw new Error('First argument needs to be ArrayBuffer.');
  				var array = new Uint8Array(buffer);
  				return this.checksum = _updateUint8Array(1, array);
  			});
  		}

  		proto.update = function update(binaryString) {
  			if (binaryString == null)
  				throw new Error('First argument needs to be a string.');
  			binaryString = binaryString.toString();
  			return this.checksum = _update(this.checksum, binaryString);
  		};

  		proto.updateUtf8 = function updateUtf8(utf8String) {
  			if (utf8String == null)
  				throw new Error('First argument needs to be a string.');
  			var binaryString = _utf8ToBinary(utf8String.toString());
  			return this.checksum = _update(this.checksum, binaryString);
  		};

  		if (_hasArrayBuffer) {
  			proto.updateBuffer = function updateBuffer(buffer) {
  				if (!_isBuffer(buffer))
  					throw new Error('First argument needs to be ArrayBuffer.');
  				var array = new Uint8Array(buffer);
  				return this.checksum = _updateUint8Array(this.checksum, array);
  			};
  		}

  		proto.clone = function clone() {
  			return new Adler32(this.checksum);
  		};

  		return ctor;
  	}());

  	exports.from = function from(binaryString) {
  		if (binaryString == null)
  			throw new Error('First argument needs to be a string.');
  		return _update(1, binaryString.toString());
  	};

  	exports.fromUtf8 = function fromUtf8(utf8String) {
  		if (utf8String == null)
  			throw new Error('First argument needs to be a string.');
  		var binaryString = _utf8ToBinary(utf8String.toString());
  		return _update(1, binaryString);
  	};

  	if (_hasArrayBuffer) {
  		exports.fromBuffer = function fromBuffer(buffer) {
  			if (!_isBuffer(buffer))
  				throw new Error('First argument need to be ArrayBuffer.');
  			var array = new Uint8Array(buffer);
  			return _updateUint8Array(1, array);
  		};
  	}

  	return exports;
  });

  /**
   * CssColors
   * Copyright (c) 2014 Steven Spungin (TwelveTone LLC)  steven@twelvetone.tv
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  /**
   * Usage CssColors('red');
   * Returns RGB hex color with '#' prefix
   */

  var CssColors = {};
  CssColors._colorsTable = {
  	"aliceblue" : "#f0f8ff",
  	"antiquewhite" : "#faebd7",
  	"aqua" : "#00ffff",
  	"aquamarine" : "#7fffd4",
  	"azure" : "#f0ffff",
  	"beige" : "#f5f5dc",
  	"bisque" : "#ffe4c4",
  	"black" : "#000000",
  	"blanchedalmond" : "#ffebcd",
  	"blue" : "#0000ff",
  	"blueviolet" : "#8a2be2",
  	"brown" : "#a52a2a",
  	"burlywood" : "#deb887",
  	"cadetblue" : "#5f9ea0",
  	"chartreuse" : "#7fff00",
  	"chocolate" : "#d2691e",
  	"coral" : "#ff7f50",
  	"cornflowerblue" : "#6495ed",
  	"cornsilk" : "#fff8dc",
  	"crimson" : "#dc143c",
  	"cyan" : "#00ffff",
  	"darkblue" : "#00008b",
  	"darkcyan" : "#008b8b",
  	"darkgoldenrod" : "#b8860b",
  	"darkgray" : "#a9a9a9",
  	"darkgreen" : "#006400",
  	"darkkhaki" : "#bdb76b",
  	"darkmagenta" : "#8b008b",
  	"darkolivegreen" : "#556b2f",
  	"darkorange" : "#ff8c00",
  	"darkorchid" : "#9932cc",
  	"darkred" : "#8b0000",
  	"darksalmon" : "#e9967a",
  	"darkseagreen" : "#8fbc8f",
  	"darkslateblue" : "#483d8b",
  	"darkslategray" : "#2f4f4f",
  	"darkturquoise" : "#00ced1",
  	"darkviolet" : "#9400d3",
  	"deeppink" : "#ff1493",
  	"deepskyblue" : "#00bfff",
  	"dimgray" : "#696969",
  	"dodgerblue" : "#1e90ff",
  	"firebrick" : "#b22222",
  	"floralwhite" : "#fffaf0",
  	"forestgreen" : "#228b22",
  	"fuchsia" : "#ff00ff",
  	"gainsboro" : "#dcdcdc",
  	"ghostwhite" : "#f8f8ff",
  	"gold" : "#ffd700",
  	"goldenrod" : "#daa520",
  	"gray" : "#808080",
  	"green" : "#008000",
  	"greenyellow" : "#adff2f",
  	"honeydew" : "#f0fff0",
  	"hotpink" : "#ff69b4",
  	"indianred " : "#cd5c5c",
  	"indigo" : "#4b0082",
  	"ivory" : "#fffff0",
  	"khaki" : "#f0e68c",
  	"lavender" : "#e6e6fa",
  	"lavenderblush" : "#fff0f5",
  	"lawngreen" : "#7cfc00",
  	"lemonchiffon" : "#fffacd",
  	"lightblue" : "#add8e6",
  	"lightcoral" : "#f08080",
  	"lightcyan" : "#e0ffff",
  	"lightgoldenrodyellow" : "#fafad2",
  	"lightgrey" : "#d3d3d3",
  	"lightgreen" : "#90ee90",
  	"lightpink" : "#ffb6c1",
  	"lightsalmon" : "#ffa07a",
  	"lightseagreen" : "#20b2aa",
  	"lightskyblue" : "#87cefa",
  	"lightslategray" : "#778899",
  	"lightsteelblue" : "#b0c4de",
  	"lightyellow" : "#ffffe0",
  	"lime" : "#00ff00",
  	"limegreen" : "#32cd32",
  	"linen" : "#faf0e6",
  	"magenta" : "#ff00ff",
  	"maroon" : "#800000",
  	"mediumaquamarine" : "#66cdaa",
  	"mediumblue" : "#0000cd",
  	"mediumorchid" : "#ba55d3",
  	"mediumpurple" : "#9370d8",
  	"mediumseagreen" : "#3cb371",
  	"mediumslateblue" : "#7b68ee",
  	"mediumspringgreen" : "#00fa9a",
  	"mediumturquoise" : "#48d1cc",
  	"mediumvioletred" : "#c71585",
  	"midnightblue" : "#191970",
  	"mintcream" : "#f5fffa",
  	"mistyrose" : "#ffe4e1",
  	"moccasin" : "#ffe4b5",
  	"navajowhite" : "#ffdead",
  	"navy" : "#000080",
  	"oldlace" : "#fdf5e6",
  	"olive" : "#808000",
  	"olivedrab" : "#6b8e23",
  	"orange" : "#ffa500",
  	"orangered" : "#ff4500",
  	"orchid" : "#da70d6",
  	"palegoldenrod" : "#eee8aa",
  	"palegreen" : "#98fb98",
  	"paleturquoise" : "#afeeee",
  	"palevioletred" : "#d87093",
  	"papayawhip" : "#ffefd5",
  	"peachpuff" : "#ffdab9",
  	"peru" : "#cd853f",
  	"pink" : "#ffc0cb",
  	"plum" : "#dda0dd",
  	"powderblue" : "#b0e0e6",
  	"purple" : "#800080",
  	"red" : "#ff0000",
  	"rosybrown" : "#bc8f8f",
  	"royalblue" : "#4169e1",
  	"saddlebrown" : "#8b4513",
  	"salmon" : "#fa8072",
  	"sandybrown" : "#f4a460",
  	"seagreen" : "#2e8b57",
  	"seashell" : "#fff5ee",
  	"sienna" : "#a0522d",
  	"silver" : "#c0c0c0",
  	"skyblue" : "#87ceeb",
  	"slateblue" : "#6a5acd",
  	"slategray" : "#708090",
  	"snow" : "#fffafa",
  	"springgreen" : "#00ff7f",
  	"steelblue" : "#4682b4",
  	"tan" : "#d2b48c",
  	"teal" : "#008080",
  	"thistle" : "#d8bfd8",
  	"tomato" : "#ff6347",
  	"turquoise" : "#40e0d0",
  	"violet" : "#ee82ee",
  	"wheat" : "#f5deb3",
  	"white" : "#ffffff",
  	"whitesmoke" : "#f5f5f5",
  	"yellow" : "#ffff00",
  	"yellowgreen" : "#9acd32"
  };

  CssColors.colorNameToHex = function(color) {
  	color = color.toLowerCase();
  	if (typeof this._colorsTable[color] != 'undefined')
  		return this._colorsTable[color];

  	return false;
  };

  /*
   Deflate.js - https://github.com/gildas-lormeau/zip.js
   Copyright (c) 2013 Gildas Lormeau. All rights reserved.

   Redistribution and use in source and binary forms, with or without
   modification, are permitted provided that the following conditions are met:

   1. Redistributions of source code must retain the above copyright notice,
   this list of conditions and the following disclaimer.

   2. Redistributions in binary form must reproduce the above copyright 
   notice, this list of conditions and the following disclaimer in 
   the documentation and/or other materials provided with the distribution.

   3. The names of the authors may not be used to endorse or promote products
   derived from this software without specific prior written permission.

   THIS SOFTWARE IS PROVIDED ``AS IS'' AND ANY EXPRESSED OR IMPLIED WARRANTIES,
   INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
   FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL JCRAFT,
   INC. OR ANY CONTRIBUTORS TO THIS SOFTWARE BE LIABLE FOR ANY DIRECT, INDIRECT,
   INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT
   LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA,
   OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
   LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING
   NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE,
   EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
   */

  /*
   * This program is based on JZlib 1.0.2 ymnk, JCraft,Inc.
   * JZlib is based on zlib-1.1.3, so all credit should go authors
   * Jean-loup Gailly(jloup@gzip.org) and Mark Adler(madler@alumni.caltech.edu)
   * and contributors of zlib.
   */

  var Deflater = (function(obj) {

  	// Global

  	var MAX_BITS = 15;
  	var D_CODES = 30;
  	var BL_CODES = 19;

  	var LENGTH_CODES = 29;
  	var LITERALS = 256;
  	var L_CODES = (LITERALS + 1 + LENGTH_CODES);
  	var HEAP_SIZE = (2 * L_CODES + 1);

  	var END_BLOCK = 256;

  	// Bit length codes must not exceed MAX_BL_BITS bits
  	var MAX_BL_BITS = 7;

  	// repeat previous bit length 3-6 times (2 bits of repeat count)
  	var REP_3_6 = 16;

  	// repeat a zero length 3-10 times (3 bits of repeat count)
  	var REPZ_3_10 = 17;

  	// repeat a zero length 11-138 times (7 bits of repeat count)
  	var REPZ_11_138 = 18;

  	// The lengths of the bit length codes are sent in order of decreasing
  	// probability, to avoid transmitting the lengths for unused bit
  	// length codes.

  	var Buf_size = 8 * 2;

  	// JZlib version : "1.0.2"
  	var Z_DEFAULT_COMPRESSION = -1;

  	// compression strategy
  	var Z_FILTERED = 1;
  	var Z_HUFFMAN_ONLY = 2;
  	var Z_DEFAULT_STRATEGY = 0;

  	var Z_NO_FLUSH = 0;
  	var Z_PARTIAL_FLUSH = 1;
  	var Z_FULL_FLUSH = 3;
  	var Z_FINISH = 4;

  	var Z_OK = 0;
  	var Z_STREAM_END = 1;
  	var Z_NEED_DICT = 2;
  	var Z_STREAM_ERROR = -2;
  	var Z_DATA_ERROR = -3;
  	var Z_BUF_ERROR = -5;

  	// Tree

  	// see definition of array dist_code below
  	var _dist_code = [ 0, 1, 2, 3, 4, 4, 5, 5, 6, 6, 6, 6, 7, 7, 7, 7, 8, 8, 8, 8, 8, 8, 8, 8, 9, 9, 9, 9, 9, 9, 9, 9, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10,
  			10, 10, 10, 10, 10, 10, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12,
  			12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13,
  			13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14,
  			14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14,
  			14, 14, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15,
  			15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 0, 0, 16, 17, 18, 18, 19, 19,
  			20, 20, 20, 20, 21, 21, 21, 21, 22, 22, 22, 22, 22, 22, 22, 22, 23, 23, 23, 23, 23, 23, 23, 23, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  			24, 24, 24, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26,
  			26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27,
  			27, 27, 27, 27, 27, 27, 27, 27, 27, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
  			28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 29,
  			29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29,
  			29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29 ];

  	function Tree() {
  		var that = this;

  		// dyn_tree; // the dynamic tree
  		// max_code; // largest code with non zero frequency
  		// stat_desc; // the corresponding static tree

  		// Compute the optimal bit lengths for a tree and update the total bit
  		// length
  		// for the current block.
  		// IN assertion: the fields freq and dad are set, heap[heap_max] and
  		// above are the tree nodes sorted by increasing frequency.
  		// OUT assertions: the field len is set to the optimal bit length, the
  		// array bl_count contains the frequencies for each bit length.
  		// The length opt_len is updated; static_len is also updated if stree is
  		// not null.
  		function gen_bitlen(s) {
  			var tree = that.dyn_tree;
  			var stree = that.stat_desc.static_tree;
  			var extra = that.stat_desc.extra_bits;
  			var base = that.stat_desc.extra_base;
  			var max_length = that.stat_desc.max_length;
  			var h; // heap index
  			var n, m; // iterate over the tree elements
  			var bits; // bit length
  			var xbits; // extra bits
  			var f; // frequency
  			var overflow = 0; // number of elements with bit length too large

  			for (bits = 0; bits <= MAX_BITS; bits++)
  				s.bl_count[bits] = 0;

  			// In a first pass, compute the optimal bit lengths (which may
  			// overflow in the case of the bit length tree).
  			tree[s.heap[s.heap_max] * 2 + 1] = 0; // root of the heap

  			for (h = s.heap_max + 1; h < HEAP_SIZE; h++) {
  				n = s.heap[h];
  				bits = tree[tree[n * 2 + 1] * 2 + 1] + 1;
  				if (bits > max_length) {
  					bits = max_length;
  					overflow++;
  				}
  				tree[n * 2 + 1] = bits;
  				// We overwrite tree[n*2+1] which is no longer needed

  				if (n > that.max_code)
  					continue; // not a leaf node

  				s.bl_count[bits]++;
  				xbits = 0;
  				if (n >= base)
  					xbits = extra[n - base];
  				f = tree[n * 2];
  				s.opt_len += f * (bits + xbits);
  				if (stree)
  					s.static_len += f * (stree[n * 2 + 1] + xbits);
  			}
  			if (overflow === 0)
  				return;

  			// This happens for example on obj2 and pic of the Calgary corpus
  			// Find the first bit length which could increase:
  			do {
  				bits = max_length - 1;
  				while (s.bl_count[bits] === 0)
  					bits--;
  				s.bl_count[bits]--; // move one leaf down the tree
  				s.bl_count[bits + 1] += 2; // move one overflow item as its brother
  				s.bl_count[max_length]--;
  				// The brother of the overflow item also moves one step up,
  				// but this does not affect bl_count[max_length]
  				overflow -= 2;
  			} while (overflow > 0);

  			for (bits = max_length; bits !== 0; bits--) {
  				n = s.bl_count[bits];
  				while (n !== 0) {
  					m = s.heap[--h];
  					if (m > that.max_code)
  						continue;
  					if (tree[m * 2 + 1] != bits) {
  						s.opt_len += (bits - tree[m * 2 + 1]) * tree[m * 2];
  						tree[m * 2 + 1] = bits;
  					}
  					n--;
  				}
  			}
  		}

  		// Reverse the first len bits of a code, using straightforward code (a
  		// faster
  		// method would use a table)
  		// IN assertion: 1 <= len <= 15
  		function bi_reverse(code, // the value to invert
  		len // its bit length
  		) {
  			var res = 0;
  			do {
  				res |= code & 1;
  				code >>>= 1;
  				res <<= 1;
  			} while (--len > 0);
  			return res >>> 1;
  		}

  		// Generate the codes for a given tree and bit counts (which need not be
  		// optimal).
  		// IN assertion: the array bl_count contains the bit length statistics for
  		// the given tree and the field len is set for all tree elements.
  		// OUT assertion: the field code is set for all tree elements of non
  		// zero code length.
  		function gen_codes(tree, // the tree to decorate
  		max_code, // largest code with non zero frequency
  		bl_count // number of codes at each bit length
  		) {
  			var next_code = []; // next code value for each
  			// bit length
  			var code = 0; // running code value
  			var bits; // bit index
  			var n; // code index
  			var len;

  			// The distribution counts are first used to generate the code values
  			// without bit reversal.
  			for (bits = 1; bits <= MAX_BITS; bits++) {
  				next_code[bits] = code = ((code + bl_count[bits - 1]) << 1);
  			}

  			// Check that the bit counts in bl_count are consistent. The last code
  			// must be all ones.
  			// Assert (code + bl_count[MAX_BITS]-1 == (1<<MAX_BITS)-1,
  			// "inconsistent bit counts");
  			// Tracev((stderr,"\ngen_codes: max_code %d ", max_code));

  			for (n = 0; n <= max_code; n++) {
  				len = tree[n * 2 + 1];
  				if (len === 0)
  					continue;
  				// Now reverse the bits
  				tree[n * 2] = bi_reverse(next_code[len]++, len);
  			}
  		}

  		// Construct one Huffman tree and assigns the code bit strings and lengths.
  		// Update the total bit length for the current block.
  		// IN assertion: the field freq is set for all tree elements.
  		// OUT assertions: the fields len and code are set to the optimal bit length
  		// and corresponding code. The length opt_len is updated; static_len is
  		// also updated if stree is not null. The field max_code is set.
  		that.build_tree = function(s) {
  			var tree = that.dyn_tree;
  			var stree = that.stat_desc.static_tree;
  			var elems = that.stat_desc.elems;
  			var n, m; // iterate over heap elements
  			var max_code = -1; // largest code with non zero frequency
  			var node; // new node being created

  			// Construct the initial heap, with least frequent element in
  			// heap[1]. The sons of heap[n] are heap[2*n] and heap[2*n+1].
  			// heap[0] is not used.
  			s.heap_len = 0;
  			s.heap_max = HEAP_SIZE;

  			for (n = 0; n < elems; n++) {
  				if (tree[n * 2] !== 0) {
  					s.heap[++s.heap_len] = max_code = n;
  					s.depth[n] = 0;
  				} else {
  					tree[n * 2 + 1] = 0;
  				}
  			}

  			// The pkzip format requires that at least one distance code exists,
  			// and that at least one bit should be sent even if there is only one
  			// possible code. So to avoid special checks later on we force at least
  			// two codes of non zero frequency.
  			while (s.heap_len < 2) {
  				node = s.heap[++s.heap_len] = max_code < 2 ? ++max_code : 0;
  				tree[node * 2] = 1;
  				s.depth[node] = 0;
  				s.opt_len--;
  				if (stree)
  					s.static_len -= stree[node * 2 + 1];
  				// node is 0 or 1 so it does not have extra bits
  			}
  			that.max_code = max_code;

  			// The elements heap[heap_len/2+1 .. heap_len] are leaves of the tree,
  			// establish sub-heaps of increasing lengths:

  			for (n = Math.floor(s.heap_len / 2); n >= 1; n--)
  				s.pqdownheap(tree, n);

  			// Construct the Huffman tree by repeatedly combining the least two
  			// frequent nodes.

  			node = elems; // next internal node of the tree
  			do {
  				// n = node of least frequency
  				n = s.heap[1];
  				s.heap[1] = s.heap[s.heap_len--];
  				s.pqdownheap(tree, 1);
  				m = s.heap[1]; // m = node of next least frequency

  				s.heap[--s.heap_max] = n; // keep the nodes sorted by frequency
  				s.heap[--s.heap_max] = m;

  				// Create a new node father of n and m
  				tree[node * 2] = (tree[n * 2] + tree[m * 2]);
  				s.depth[node] = Math.max(s.depth[n], s.depth[m]) + 1;
  				tree[n * 2 + 1] = tree[m * 2 + 1] = node;

  				// and insert the new node in the heap
  				s.heap[1] = node++;
  				s.pqdownheap(tree, 1);
  			} while (s.heap_len >= 2);

  			s.heap[--s.heap_max] = s.heap[1];

  			// At this point, the fields freq and dad are set. We can now
  			// generate the bit lengths.

  			gen_bitlen(s);

  			// The field len is now set, we can generate the bit codes
  			gen_codes(tree, that.max_code, s.bl_count);
  		};

  	}

  	Tree._length_code = [ 0, 1, 2, 3, 4, 5, 6, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 12, 12, 13, 13, 13, 13, 14, 14, 14, 14, 15, 15, 15, 15, 16, 16, 16, 16,
  			16, 16, 16, 16, 17, 17, 17, 17, 17, 17, 17, 17, 18, 18, 18, 18, 18, 18, 18, 18, 19, 19, 19, 19, 19, 19, 19, 19, 20, 20, 20, 20, 20, 20, 20, 20, 20,
  			20, 20, 20, 20, 20, 20, 20, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 22, 22, 22, 22, 22, 22, 22, 22, 22, 22, 22, 22, 22, 22,
  			22, 22, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  			24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25,
  			25, 25, 25, 25, 25, 25, 25, 25, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26,
  			26, 26, 26, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 28 ];

  	Tree.base_length = [ 0, 1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 14, 16, 20, 24, 28, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 0 ];

  	Tree.base_dist = [ 0, 1, 2, 3, 4, 6, 8, 12, 16, 24, 32, 48, 64, 96, 128, 192, 256, 384, 512, 768, 1024, 1536, 2048, 3072, 4096, 6144, 8192, 12288, 16384,
  			24576 ];

  	// Mapping from a distance to a distance code. dist is the distance - 1 and
  	// must not have side effects. _dist_code[256] and _dist_code[257] are never
  	// used.
  	Tree.d_code = function(dist) {
  		return ((dist) < 256 ? _dist_code[dist] : _dist_code[256 + ((dist) >>> 7)]);
  	};

  	// extra bits for each length code
  	Tree.extra_lbits = [ 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0 ];

  	// extra bits for each distance code
  	Tree.extra_dbits = [ 0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13 ];

  	// extra bits for each bit length code
  	Tree.extra_blbits = [ 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 2, 3, 7 ];

  	Tree.bl_order = [ 16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15 ];

  	// StaticTree

  	function StaticTree(static_tree, extra_bits, extra_base, elems, max_length) {
  		var that = this;
  		that.static_tree = static_tree;
  		that.extra_bits = extra_bits;
  		that.extra_base = extra_base;
  		that.elems = elems;
  		that.max_length = max_length;
  	}

  	StaticTree.static_ltree = [ 12, 8, 140, 8, 76, 8, 204, 8, 44, 8, 172, 8, 108, 8, 236, 8, 28, 8, 156, 8, 92, 8, 220, 8, 60, 8, 188, 8, 124, 8, 252, 8, 2, 8,
  			130, 8, 66, 8, 194, 8, 34, 8, 162, 8, 98, 8, 226, 8, 18, 8, 146, 8, 82, 8, 210, 8, 50, 8, 178, 8, 114, 8, 242, 8, 10, 8, 138, 8, 74, 8, 202, 8, 42,
  			8, 170, 8, 106, 8, 234, 8, 26, 8, 154, 8, 90, 8, 218, 8, 58, 8, 186, 8, 122, 8, 250, 8, 6, 8, 134, 8, 70, 8, 198, 8, 38, 8, 166, 8, 102, 8, 230, 8,
  			22, 8, 150, 8, 86, 8, 214, 8, 54, 8, 182, 8, 118, 8, 246, 8, 14, 8, 142, 8, 78, 8, 206, 8, 46, 8, 174, 8, 110, 8, 238, 8, 30, 8, 158, 8, 94, 8,
  			222, 8, 62, 8, 190, 8, 126, 8, 254, 8, 1, 8, 129, 8, 65, 8, 193, 8, 33, 8, 161, 8, 97, 8, 225, 8, 17, 8, 145, 8, 81, 8, 209, 8, 49, 8, 177, 8, 113,
  			8, 241, 8, 9, 8, 137, 8, 73, 8, 201, 8, 41, 8, 169, 8, 105, 8, 233, 8, 25, 8, 153, 8, 89, 8, 217, 8, 57, 8, 185, 8, 121, 8, 249, 8, 5, 8, 133, 8,
  			69, 8, 197, 8, 37, 8, 165, 8, 101, 8, 229, 8, 21, 8, 149, 8, 85, 8, 213, 8, 53, 8, 181, 8, 117, 8, 245, 8, 13, 8, 141, 8, 77, 8, 205, 8, 45, 8,
  			173, 8, 109, 8, 237, 8, 29, 8, 157, 8, 93, 8, 221, 8, 61, 8, 189, 8, 125, 8, 253, 8, 19, 9, 275, 9, 147, 9, 403, 9, 83, 9, 339, 9, 211, 9, 467, 9,
  			51, 9, 307, 9, 179, 9, 435, 9, 115, 9, 371, 9, 243, 9, 499, 9, 11, 9, 267, 9, 139, 9, 395, 9, 75, 9, 331, 9, 203, 9, 459, 9, 43, 9, 299, 9, 171, 9,
  			427, 9, 107, 9, 363, 9, 235, 9, 491, 9, 27, 9, 283, 9, 155, 9, 411, 9, 91, 9, 347, 9, 219, 9, 475, 9, 59, 9, 315, 9, 187, 9, 443, 9, 123, 9, 379,
  			9, 251, 9, 507, 9, 7, 9, 263, 9, 135, 9, 391, 9, 71, 9, 327, 9, 199, 9, 455, 9, 39, 9, 295, 9, 167, 9, 423, 9, 103, 9, 359, 9, 231, 9, 487, 9, 23,
  			9, 279, 9, 151, 9, 407, 9, 87, 9, 343, 9, 215, 9, 471, 9, 55, 9, 311, 9, 183, 9, 439, 9, 119, 9, 375, 9, 247, 9, 503, 9, 15, 9, 271, 9, 143, 9,
  			399, 9, 79, 9, 335, 9, 207, 9, 463, 9, 47, 9, 303, 9, 175, 9, 431, 9, 111, 9, 367, 9, 239, 9, 495, 9, 31, 9, 287, 9, 159, 9, 415, 9, 95, 9, 351, 9,
  			223, 9, 479, 9, 63, 9, 319, 9, 191, 9, 447, 9, 127, 9, 383, 9, 255, 9, 511, 9, 0, 7, 64, 7, 32, 7, 96, 7, 16, 7, 80, 7, 48, 7, 112, 7, 8, 7, 72, 7,
  			40, 7, 104, 7, 24, 7, 88, 7, 56, 7, 120, 7, 4, 7, 68, 7, 36, 7, 100, 7, 20, 7, 84, 7, 52, 7, 116, 7, 3, 8, 131, 8, 67, 8, 195, 8, 35, 8, 163, 8,
  			99, 8, 227, 8 ];

  	StaticTree.static_dtree = [ 0, 5, 16, 5, 8, 5, 24, 5, 4, 5, 20, 5, 12, 5, 28, 5, 2, 5, 18, 5, 10, 5, 26, 5, 6, 5, 22, 5, 14, 5, 30, 5, 1, 5, 17, 5, 9, 5,
  			25, 5, 5, 5, 21, 5, 13, 5, 29, 5, 3, 5, 19, 5, 11, 5, 27, 5, 7, 5, 23, 5 ];

  	StaticTree.static_l_desc = new StaticTree(StaticTree.static_ltree, Tree.extra_lbits, LITERALS + 1, L_CODES, MAX_BITS);

  	StaticTree.static_d_desc = new StaticTree(StaticTree.static_dtree, Tree.extra_dbits, 0, D_CODES, MAX_BITS);

  	StaticTree.static_bl_desc = new StaticTree(null, Tree.extra_blbits, 0, BL_CODES, MAX_BL_BITS);

  	// Deflate

  	var MAX_MEM_LEVEL = 9;
  	var DEF_MEM_LEVEL = 8;

  	function Config(good_length, max_lazy, nice_length, max_chain, func) {
  		var that = this;
  		that.good_length = good_length;
  		that.max_lazy = max_lazy;
  		that.nice_length = nice_length;
  		that.max_chain = max_chain;
  		that.func = func;
  	}

  	var STORED = 0;
  	var FAST = 1;
  	var SLOW = 2;
  	var config_table = [ new Config(0, 0, 0, 0, STORED), new Config(4, 4, 8, 4, FAST), new Config(4, 5, 16, 8, FAST), new Config(4, 6, 32, 32, FAST),
  			new Config(4, 4, 16, 16, SLOW), new Config(8, 16, 32, 32, SLOW), new Config(8, 16, 128, 128, SLOW), new Config(8, 32, 128, 256, SLOW),
  			new Config(32, 128, 258, 1024, SLOW), new Config(32, 258, 258, 4096, SLOW) ];

  	var z_errmsg = [ "need dictionary", // Z_NEED_DICT
  	// 2
  	"stream end", // Z_STREAM_END 1
  	"", // Z_OK 0
  	"", // Z_ERRNO (-1)
  	"stream error", // Z_STREAM_ERROR (-2)
  	"data error", // Z_DATA_ERROR (-3)
  	"", // Z_MEM_ERROR (-4)
  	"buffer error", // Z_BUF_ERROR (-5)
  	"",// Z_VERSION_ERROR (-6)
  	"" ];

  	// block not completed, need more input or more output
  	var NeedMore = 0;

  	// block flush performed
  	var BlockDone = 1;

  	// finish started, need only more output at next deflate
  	var FinishStarted = 2;

  	// finish done, accept no more input or output
  	var FinishDone = 3;

  	// preset dictionary flag in zlib header
  	var PRESET_DICT = 0x20;

  	var INIT_STATE = 42;
  	var BUSY_STATE = 113;
  	var FINISH_STATE = 666;

  	// The deflate compression method
  	var Z_DEFLATED = 8;

  	var STORED_BLOCK = 0;
  	var STATIC_TREES = 1;
  	var DYN_TREES = 2;

  	var MIN_MATCH = 3;
  	var MAX_MATCH = 258;
  	var MIN_LOOKAHEAD = (MAX_MATCH + MIN_MATCH + 1);

  	function smaller(tree, n, m, depth) {
  		var tn2 = tree[n * 2];
  		var tm2 = tree[m * 2];
  		return (tn2 < tm2 || (tn2 == tm2 && depth[n] <= depth[m]));
  	}

  	function Deflate() {

  		var that = this;
  		var strm; // pointer back to this zlib stream
  		var status; // as the name implies
  		// pending_buf; // output still pending
  		var pending_buf_size; // size of pending_buf
  		// pending_out; // next pending byte to output to the stream
  		// pending; // nb of bytes in the pending buffer
  		var method; // STORED (for zip only) or DEFLATED
  		var last_flush; // value of flush param for previous deflate call

  		var w_size; // LZ77 window size (32K by default)
  		var w_bits; // log2(w_size) (8..16)
  		var w_mask; // w_size - 1

  		var window;
  		// Sliding window. Input bytes are read into the second half of the window,
  		// and move to the first half later to keep a dictionary of at least wSize
  		// bytes. With this organization, matches are limited to a distance of
  		// wSize-MAX_MATCH bytes, but this ensures that IO is always
  		// performed with a length multiple of the block size. Also, it limits
  		// the window size to 64K, which is quite useful on MSDOS.
  		// To do: use the user input buffer as sliding window.

  		var window_size;
  		// Actual size of window: 2*wSize, except when the user input buffer
  		// is directly used as sliding window.

  		var prev;
  		// Link to older string with same hash index. To limit the size of this
  		// array to 64K, this link is maintained only for the last 32K strings.
  		// An index in this array is thus a window index modulo 32K.

  		var head; // Heads of the hash chains or NIL.

  		var ins_h; // hash index of string to be inserted
  		var hash_size; // number of elements in hash table
  		var hash_bits; // log2(hash_size)
  		var hash_mask; // hash_size-1

  		// Number of bits by which ins_h must be shifted at each input
  		// step. It must be such that after MIN_MATCH steps, the oldest
  		// byte no longer takes part in the hash key, that is:
  		// hash_shift * MIN_MATCH >= hash_bits
  		var hash_shift;

  		// Window position at the beginning of the current output block. Gets
  		// negative when the window is moved backwards.

  		var block_start;

  		var match_length; // length of best match
  		var prev_match; // previous match
  		var match_available; // set if previous match exists
  		var strstart; // start of string to insert
  		var match_start; // start of matching string
  		var lookahead; // number of valid bytes ahead in window

  		// Length of the best match at previous step. Matches not greater than this
  		// are discarded. This is used in the lazy match evaluation.
  		var prev_length;

  		// To speed up deflation, hash chains are never searched beyond this
  		// length. A higher limit improves compression ratio but degrades the speed.
  		var max_chain_length;

  		// Attempt to find a better match only when the current match is strictly
  		// smaller than this value. This mechanism is used only for compression
  		// levels >= 4.
  		var max_lazy_match;

  		// Insert new strings in the hash table only if the match length is not
  		// greater than this length. This saves time but degrades compression.
  		// max_insert_length is used only for compression levels <= 3.

  		var level; // compression level (1..9)
  		var strategy; // favor or force Huffman coding

  		// Use a faster search when the previous match is longer than this
  		var good_match;

  		// Stop searching when current match exceeds this
  		var nice_match;

  		var dyn_ltree; // literal and length tree
  		var dyn_dtree; // distance tree
  		var bl_tree; // Huffman tree for bit lengths

  		var l_desc = new Tree(); // desc for literal tree
  		var d_desc = new Tree(); // desc for distance tree
  		var bl_desc = new Tree(); // desc for bit length tree

  		// that.heap_len; // number of elements in the heap
  		// that.heap_max; // element of largest frequency
  		// The sons of heap[n] are heap[2*n] and heap[2*n+1]. heap[0] is not used.
  		// The same heap array is used to build all trees.

  		// Depth of each subtree used as tie breaker for trees of equal frequency
  		that.depth = [];

  		var l_buf; // index for literals or lengths */

  		// Size of match buffer for literals/lengths. There are 4 reasons for
  		// limiting lit_bufsize to 64K:
  		// - frequencies can be kept in 16 bit counters
  		// - if compression is not successful for the first block, all input
  		// data is still in the window so we can still emit a stored block even
  		// when input comes from standard input. (This can also be done for
  		// all blocks if lit_bufsize is not greater than 32K.)
  		// - if compression is not successful for a file smaller than 64K, we can
  		// even emit a stored file instead of a stored block (saving 5 bytes).
  		// This is applicable only for zip (not gzip or zlib).
  		// - creating new Huffman trees less frequently may not provide fast
  		// adaptation to changes in the input data statistics. (Take for
  		// example a binary file with poorly compressible code followed by
  		// a highly compressible string table.) Smaller buffer sizes give
  		// fast adaptation but have of course the overhead of transmitting
  		// trees more frequently.
  		// - I can't count above 4
  		var lit_bufsize;

  		var last_lit; // running index in l_buf

  		// Buffer for distances. To simplify the code, d_buf and l_buf have
  		// the same number of elements. To use different lengths, an extra flag
  		// array would be necessary.

  		var d_buf; // index of pendig_buf

  		// that.opt_len; // bit length of current block with optimal trees
  		// that.static_len; // bit length of current block with static trees
  		var matches; // number of string matches in current block
  		var last_eob_len; // bit length of EOB code for last block

  		// Output buffer. bits are inserted starting at the bottom (least
  		// significant bits).
  		var bi_buf;

  		// Number of valid bits in bi_buf. All bits above the last valid bit
  		// are always zero.
  		var bi_valid;

  		// number of codes at each bit length for an optimal tree
  		that.bl_count = [];

  		// heap used to build the Huffman trees
  		that.heap = [];

  		dyn_ltree = [];
  		dyn_dtree = [];
  		bl_tree = [];

  		function lm_init() {
  			var i;
  			window_size = 2 * w_size;

  			head[hash_size - 1] = 0;
  			for (i = 0; i < hash_size - 1; i++) {
  				head[i] = 0;
  			}

  			// Set the default configuration parameters:
  			max_lazy_match = config_table[level].max_lazy;
  			good_match = config_table[level].good_length;
  			nice_match = config_table[level].nice_length;
  			max_chain_length = config_table[level].max_chain;

  			strstart = 0;
  			block_start = 0;
  			lookahead = 0;
  			match_length = prev_length = MIN_MATCH - 1;
  			match_available = 0;
  			ins_h = 0;
  		}

  		function init_block() {
  			var i;
  			// Initialize the trees.
  			for (i = 0; i < L_CODES; i++)
  				dyn_ltree[i * 2] = 0;
  			for (i = 0; i < D_CODES; i++)
  				dyn_dtree[i * 2] = 0;
  			for (i = 0; i < BL_CODES; i++)
  				bl_tree[i * 2] = 0;

  			dyn_ltree[END_BLOCK * 2] = 1;
  			that.opt_len = that.static_len = 0;
  			last_lit = matches = 0;
  		}

  		// Initialize the tree data structures for a new zlib stream.
  		function tr_init() {

  			l_desc.dyn_tree = dyn_ltree;
  			l_desc.stat_desc = StaticTree.static_l_desc;

  			d_desc.dyn_tree = dyn_dtree;
  			d_desc.stat_desc = StaticTree.static_d_desc;

  			bl_desc.dyn_tree = bl_tree;
  			bl_desc.stat_desc = StaticTree.static_bl_desc;

  			bi_buf = 0;
  			bi_valid = 0;
  			last_eob_len = 8; // enough lookahead for inflate

  			// Initialize the first block of the first file:
  			init_block();
  		}

  		// Restore the heap property by moving down the tree starting at node k,
  		// exchanging a node with the smallest of its two sons if necessary,
  		// stopping
  		// when the heap property is re-established (each father smaller than its
  		// two sons).
  		that.pqdownheap = function(tree, // the tree to restore
  		k // node to move down
  		) {
  			var heap = that.heap;
  			var v = heap[k];
  			var j = k << 1; // left son of k
  			while (j <= that.heap_len) {
  				// Set j to the smallest of the two sons:
  				if (j < that.heap_len && smaller(tree, heap[j + 1], heap[j], that.depth)) {
  					j++;
  				}
  				// Exit if v is smaller than both sons
  				if (smaller(tree, v, heap[j], that.depth))
  					break;

  				// Exchange v with the smallest son
  				heap[k] = heap[j];
  				k = j;
  				// And continue down the tree, setting j to the left son of k
  				j <<= 1;
  			}
  			heap[k] = v;
  		};

  		// Scan a literal or distance tree to determine the frequencies of the codes
  		// in the bit length tree.
  		function scan_tree(tree,// the tree to be scanned
  		max_code // and its largest code of non zero frequency
  		) {
  			var n; // iterates over all tree elements
  			var prevlen = -1; // last emitted length
  			var curlen; // length of current code
  			var nextlen = tree[0 * 2 + 1]; // length of next code
  			var count = 0; // repeat count of the current code
  			var max_count = 7; // max repeat count
  			var min_count = 4; // min repeat count

  			if (nextlen === 0) {
  				max_count = 138;
  				min_count = 3;
  			}
  			tree[(max_code + 1) * 2 + 1] = 0xffff; // guard

  			for (n = 0; n <= max_code; n++) {
  				curlen = nextlen;
  				nextlen = tree[(n + 1) * 2 + 1];
  				if (++count < max_count && curlen == nextlen) {
  					continue;
  				} else if (count < min_count) {
  					bl_tree[curlen * 2] += count;
  				} else if (curlen !== 0) {
  					if (curlen != prevlen)
  						bl_tree[curlen * 2]++;
  					bl_tree[REP_3_6 * 2]++;
  				} else if (count <= 10) {
  					bl_tree[REPZ_3_10 * 2]++;
  				} else {
  					bl_tree[REPZ_11_138 * 2]++;
  				}
  				count = 0;
  				prevlen = curlen;
  				if (nextlen === 0) {
  					max_count = 138;
  					min_count = 3;
  				} else if (curlen == nextlen) {
  					max_count = 6;
  					min_count = 3;
  				} else {
  					max_count = 7;
  					min_count = 4;
  				}
  			}
  		}

  		// Construct the Huffman tree for the bit lengths and return the index in
  		// bl_order of the last bit length code to send.
  		function build_bl_tree() {
  			var max_blindex; // index of last bit length code of non zero freq

  			// Determine the bit length frequencies for literal and distance trees
  			scan_tree(dyn_ltree, l_desc.max_code);
  			scan_tree(dyn_dtree, d_desc.max_code);

  			// Build the bit length tree:
  			bl_desc.build_tree(that);
  			// opt_len now includes the length of the tree representations, except
  			// the lengths of the bit lengths codes and the 5+5+4 bits for the
  			// counts.

  			// Determine the number of bit length codes to send. The pkzip format
  			// requires that at least 4 bit length codes be sent. (appnote.txt says
  			// 3 but the actual value used is 4.)
  			for (max_blindex = BL_CODES - 1; max_blindex >= 3; max_blindex--) {
  				if (bl_tree[Tree.bl_order[max_blindex] * 2 + 1] !== 0)
  					break;
  			}
  			// Update opt_len to include the bit length tree and counts
  			that.opt_len += 3 * (max_blindex + 1) + 5 + 5 + 4;

  			return max_blindex;
  		}

  		// Output a byte on the stream.
  		// IN assertion: there is enough room in pending_buf.
  		function put_byte(p) {
  			that.pending_buf[that.pending++] = p;
  		}

  		function put_short(w) {
  			put_byte(w & 0xff);
  			put_byte((w >>> 8) & 0xff);
  		}

  		function putShortMSB(b) {
  			put_byte((b >> 8) & 0xff);
  			put_byte((b & 0xff) & 0xff);
  		}

  		function send_bits(value, length) {
  			var val, len = length;
  			if (bi_valid > Buf_size - len) {
  				val = value;
  				// bi_buf |= (val << bi_valid);
  				bi_buf |= ((val << bi_valid) & 0xffff);
  				put_short(bi_buf);
  				bi_buf = val >>> (Buf_size - bi_valid);
  				bi_valid += len - Buf_size;
  			} else {
  				// bi_buf |= (value) << bi_valid;
  				bi_buf |= (((value) << bi_valid) & 0xffff);
  				bi_valid += len;
  			}
  		}

  		function send_code(c, tree) {
  			var c2 = c * 2;
  			send_bits(tree[c2] & 0xffff, tree[c2 + 1] & 0xffff);
  		}

  		// Send a literal or distance tree in compressed form, using the codes in
  		// bl_tree.
  		function send_tree(tree,// the tree to be sent
  		max_code // and its largest code of non zero frequency
  		) {
  			var n; // iterates over all tree elements
  			var prevlen = -1; // last emitted length
  			var curlen; // length of current code
  			var nextlen = tree[0 * 2 + 1]; // length of next code
  			var count = 0; // repeat count of the current code
  			var max_count = 7; // max repeat count
  			var min_count = 4; // min repeat count

  			if (nextlen === 0) {
  				max_count = 138;
  				min_count = 3;
  			}

  			for (n = 0; n <= max_code; n++) {
  				curlen = nextlen;
  				nextlen = tree[(n + 1) * 2 + 1];
  				if (++count < max_count && curlen == nextlen) {
  					continue;
  				} else if (count < min_count) {
  					do {
  						send_code(curlen, bl_tree);
  					} while (--count !== 0);
  				} else if (curlen !== 0) {
  					if (curlen != prevlen) {
  						send_code(curlen, bl_tree);
  						count--;
  					}
  					send_code(REP_3_6, bl_tree);
  					send_bits(count - 3, 2);
  				} else if (count <= 10) {
  					send_code(REPZ_3_10, bl_tree);
  					send_bits(count - 3, 3);
  				} else {
  					send_code(REPZ_11_138, bl_tree);
  					send_bits(count - 11, 7);
  				}
  				count = 0;
  				prevlen = curlen;
  				if (nextlen === 0) {
  					max_count = 138;
  					min_count = 3;
  				} else if (curlen == nextlen) {
  					max_count = 6;
  					min_count = 3;
  				} else {
  					max_count = 7;
  					min_count = 4;
  				}
  			}
  		}

  		// Send the header for a block using dynamic Huffman trees: the counts, the
  		// lengths of the bit length codes, the literal tree and the distance tree.
  		// IN assertion: lcodes >= 257, dcodes >= 1, blcodes >= 4.
  		function send_all_trees(lcodes, dcodes, blcodes) {
  			var rank; // index in bl_order

  			send_bits(lcodes - 257, 5); // not +255 as stated in appnote.txt
  			send_bits(dcodes - 1, 5);
  			send_bits(blcodes - 4, 4); // not -3 as stated in appnote.txt
  			for (rank = 0; rank < blcodes; rank++) {
  				send_bits(bl_tree[Tree.bl_order[rank] * 2 + 1], 3);
  			}
  			send_tree(dyn_ltree, lcodes - 1); // literal tree
  			send_tree(dyn_dtree, dcodes - 1); // distance tree
  		}

  		// Flush the bit buffer, keeping at most 7 bits in it.
  		function bi_flush() {
  			if (bi_valid == 16) {
  				put_short(bi_buf);
  				bi_buf = 0;
  				bi_valid = 0;
  			} else if (bi_valid >= 8) {
  				put_byte(bi_buf & 0xff);
  				bi_buf >>>= 8;
  				bi_valid -= 8;
  			}
  		}

  		// Send one empty static block to give enough lookahead for inflate.
  		// This takes 10 bits, of which 7 may remain in the bit buffer.
  		// The current inflate code requires 9 bits of lookahead. If the
  		// last two codes for the previous block (real code plus EOB) were coded
  		// on 5 bits or less, inflate may have only 5+3 bits of lookahead to decode
  		// the last real code. In this case we send two empty static blocks instead
  		// of one. (There are no problems if the previous block is stored or fixed.)
  		// To simplify the code, we assume the worst case of last real code encoded
  		// on one bit only.
  		function _tr_align() {
  			send_bits(STATIC_TREES << 1, 3);
  			send_code(END_BLOCK, StaticTree.static_ltree);

  			bi_flush();

  			// Of the 10 bits for the empty block, we have already sent
  			// (10 - bi_valid) bits. The lookahead for the last real code (before
  			// the EOB of the previous block) was thus at least one plus the length
  			// of the EOB plus what we have just sent of the empty static block.
  			if (1 + last_eob_len + 10 - bi_valid < 9) {
  				send_bits(STATIC_TREES << 1, 3);
  				send_code(END_BLOCK, StaticTree.static_ltree);
  				bi_flush();
  			}
  			last_eob_len = 7;
  		}

  		// Save the match info and tally the frequency counts. Return true if
  		// the current block must be flushed.
  		function _tr_tally(dist, // distance of matched string
  		lc // match length-MIN_MATCH or unmatched char (if dist==0)
  		) {
  			var out_length, in_length, dcode;
  			that.pending_buf[d_buf + last_lit * 2] = (dist >>> 8) & 0xff;
  			that.pending_buf[d_buf + last_lit * 2 + 1] = dist & 0xff;

  			that.pending_buf[l_buf + last_lit] = lc & 0xff;
  			last_lit++;

  			if (dist === 0) {
  				// lc is the unmatched char
  				dyn_ltree[lc * 2]++;
  			} else {
  				matches++;
  				// Here, lc is the match length - MIN_MATCH
  				dist--; // dist = match distance - 1
  				dyn_ltree[(Tree._length_code[lc] + LITERALS + 1) * 2]++;
  				dyn_dtree[Tree.d_code(dist) * 2]++;
  			}

  			if ((last_lit & 0x1fff) === 0 && level > 2) {
  				// Compute an upper bound for the compressed length
  				out_length = last_lit * 8;
  				in_length = strstart - block_start;
  				for (dcode = 0; dcode < D_CODES; dcode++) {
  					out_length += dyn_dtree[dcode * 2] * (5 + Tree.extra_dbits[dcode]);
  				}
  				out_length >>>= 3;
  				if ((matches < Math.floor(last_lit / 2)) && out_length < Math.floor(in_length / 2))
  					return true;
  			}

  			return (last_lit == lit_bufsize - 1);
  			// We avoid equality with lit_bufsize because of wraparound at 64K
  			// on 16 bit machines and because stored blocks are restricted to
  			// 64K-1 bytes.
  		}

  		// Send the block data compressed using the given Huffman trees
  		function compress_block(ltree, dtree) {
  			var dist; // distance of matched string
  			var lc; // match length or unmatched char (if dist === 0)
  			var lx = 0; // running index in l_buf
  			var code; // the code to send
  			var extra; // number of extra bits to send

  			if (last_lit !== 0) {
  				do {
  					dist = ((that.pending_buf[d_buf + lx * 2] << 8) & 0xff00) | (that.pending_buf[d_buf + lx * 2 + 1] & 0xff);
  					lc = (that.pending_buf[l_buf + lx]) & 0xff;
  					lx++;

  					if (dist === 0) {
  						send_code(lc, ltree); // send a literal byte
  					} else {
  						// Here, lc is the match length - MIN_MATCH
  						code = Tree._length_code[lc];

  						send_code(code + LITERALS + 1, ltree); // send the length
  						// code
  						extra = Tree.extra_lbits[code];
  						if (extra !== 0) {
  							lc -= Tree.base_length[code];
  							send_bits(lc, extra); // send the extra length bits
  						}
  						dist--; // dist is now the match distance - 1
  						code = Tree.d_code(dist);

  						send_code(code, dtree); // send the distance code
  						extra = Tree.extra_dbits[code];
  						if (extra !== 0) {
  							dist -= Tree.base_dist[code];
  							send_bits(dist, extra); // send the extra distance bits
  						}
  					} // literal or match pair ?

  					// Check that the overlay between pending_buf and d_buf+l_buf is
  					// ok:
  				} while (lx < last_lit);
  			}

  			send_code(END_BLOCK, ltree);
  			last_eob_len = ltree[END_BLOCK * 2 + 1];
  		}

  		// Flush the bit buffer and align the output on a byte boundary
  		function bi_windup() {
  			if (bi_valid > 8) {
  				put_short(bi_buf);
  			} else if (bi_valid > 0) {
  				put_byte(bi_buf & 0xff);
  			}
  			bi_buf = 0;
  			bi_valid = 0;
  		}

  		// Copy a stored block, storing first the length and its
  		// one's complement if requested.
  		function copy_block(buf, // the input data
  		len, // its length
  		header // true if block header must be written
  		) {
  			bi_windup(); // align on byte boundary
  			last_eob_len = 8; // enough lookahead for inflate

  			if (header) {
  				put_short(len);
  				put_short(~len);
  			}

  			that.pending_buf.set(window.subarray(buf, buf + len), that.pending);
  			that.pending += len;
  		}

  		// Send a stored block
  		function _tr_stored_block(buf, // input block
  		stored_len, // length of input block
  		eof // true if this is the last block for a file
  		) {
  			send_bits((STORED_BLOCK << 1) + (eof ? 1 : 0), 3); // send block type
  			copy_block(buf, stored_len, true); // with header
  		}

  		// Determine the best encoding for the current block: dynamic trees, static
  		// trees or store, and output the encoded block to the zip file.
  		function _tr_flush_block(buf, // input block, or NULL if too old
  		stored_len, // length of input block
  		eof // true if this is the last block for a file
  		) {
  			var opt_lenb, static_lenb;// opt_len and static_len in bytes
  			var max_blindex = 0; // index of last bit length code of non zero freq

  			// Build the Huffman trees unless a stored block is forced
  			if (level > 0) {
  				// Construct the literal and distance trees
  				l_desc.build_tree(that);

  				d_desc.build_tree(that);

  				// At this point, opt_len and static_len are the total bit lengths
  				// of
  				// the compressed block data, excluding the tree representations.

  				// Build the bit length tree for the above two trees, and get the
  				// index
  				// in bl_order of the last bit length code to send.
  				max_blindex = build_bl_tree();

  				// Determine the best encoding. Compute first the block length in
  				// bytes
  				opt_lenb = (that.opt_len + 3 + 7) >>> 3;
  				static_lenb = (that.static_len + 3 + 7) >>> 3;

  				if (static_lenb <= opt_lenb)
  					opt_lenb = static_lenb;
  			} else {
  				opt_lenb = static_lenb = stored_len + 5; // force a stored block
  			}

  			if ((stored_len + 4 <= opt_lenb) && buf != -1) {
  				// 4: two words for the lengths
  				// The test buf != NULL is only necessary if LIT_BUFSIZE > WSIZE.
  				// Otherwise we can't have processed more than WSIZE input bytes
  				// since
  				// the last block flush, because compression would have been
  				// successful. If LIT_BUFSIZE <= WSIZE, it is never too late to
  				// transform a block into a stored block.
  				_tr_stored_block(buf, stored_len, eof);
  			} else if (static_lenb == opt_lenb) {
  				send_bits((STATIC_TREES << 1) + (eof ? 1 : 0), 3);
  				compress_block(StaticTree.static_ltree, StaticTree.static_dtree);
  			} else {
  				send_bits((DYN_TREES << 1) + (eof ? 1 : 0), 3);
  				send_all_trees(l_desc.max_code + 1, d_desc.max_code + 1, max_blindex + 1);
  				compress_block(dyn_ltree, dyn_dtree);
  			}

  			// The above check is made mod 2^32, for files larger than 512 MB
  			// and uLong implemented on 32 bits.

  			init_block();

  			if (eof) {
  				bi_windup();
  			}
  		}

  		function flush_block_only(eof) {
  			_tr_flush_block(block_start >= 0 ? block_start : -1, strstart - block_start, eof);
  			block_start = strstart;
  			strm.flush_pending();
  		}

  		// Fill the window when the lookahead becomes insufficient.
  		// Updates strstart and lookahead.
  		//
  		// IN assertion: lookahead < MIN_LOOKAHEAD
  		// OUT assertions: strstart <= window_size-MIN_LOOKAHEAD
  		// At least one byte has been read, or avail_in === 0; reads are
  		// performed for at least two bytes (required for the zip translate_eol
  		// option -- not supported here).
  		function fill_window() {
  			var n, m;
  			var p;
  			var more; // Amount of free space at the end of the window.

  			do {
  				more = (window_size - lookahead - strstart);

  				// Deal with !@#$% 64K limit:
  				if (more === 0 && strstart === 0 && lookahead === 0) {
  					more = w_size;
  				} else if (more == -1) {
  					// Very unlikely, but possible on 16 bit machine if strstart ==
  					// 0
  					// and lookahead == 1 (input done one byte at time)
  					more--;

  					// If the window is almost full and there is insufficient
  					// lookahead,
  					// move the upper half to the lower one to make room in the
  					// upper half.
  				} else if (strstart >= w_size + w_size - MIN_LOOKAHEAD) {
  					window.set(window.subarray(w_size, w_size + w_size), 0);

  					match_start -= w_size;
  					strstart -= w_size; // we now have strstart >= MAX_DIST
  					block_start -= w_size;

  					// Slide the hash table (could be avoided with 32 bit values
  					// at the expense of memory usage). We slide even when level ==
  					// 0
  					// to keep the hash table consistent if we switch back to level
  					// > 0
  					// later. (Using level 0 permanently is not an optimal usage of
  					// zlib, so we don't care about this pathological case.)

  					n = hash_size;
  					p = n;
  					do {
  						m = (head[--p] & 0xffff);
  						head[p] = (m >= w_size ? m - w_size : 0);
  					} while (--n !== 0);

  					n = w_size;
  					p = n;
  					do {
  						m = (prev[--p] & 0xffff);
  						prev[p] = (m >= w_size ? m - w_size : 0);
  						// If n is not on any hash chain, prev[n] is garbage but
  						// its value will never be used.
  					} while (--n !== 0);
  					more += w_size;
  				}

  				if (strm.avail_in === 0)
  					return;

  				// If there was no sliding:
  				// strstart <= WSIZE+MAX_DIST-1 && lookahead <= MIN_LOOKAHEAD - 1 &&
  				// more == window_size - lookahead - strstart
  				// => more >= window_size - (MIN_LOOKAHEAD-1 + WSIZE + MAX_DIST-1)
  				// => more >= window_size - 2*WSIZE + 2
  				// In the BIG_MEM or MMAP case (not yet supported),
  				// window_size == input_size + MIN_LOOKAHEAD &&
  				// strstart + s->lookahead <= input_size => more >= MIN_LOOKAHEAD.
  				// Otherwise, window_size == 2*WSIZE so more >= 2.
  				// If there was sliding, more >= WSIZE. So in all cases, more >= 2.

  				n = strm.read_buf(window, strstart + lookahead, more);
  				lookahead += n;

  				// Initialize the hash value now that we have some input:
  				if (lookahead >= MIN_MATCH) {
  					ins_h = window[strstart] & 0xff;
  					ins_h = (((ins_h) << hash_shift) ^ (window[strstart + 1] & 0xff)) & hash_mask;
  				}
  				// If the whole input has less than MIN_MATCH bytes, ins_h is
  				// garbage,
  				// but this is not important since only literal bytes will be
  				// emitted.
  			} while (lookahead < MIN_LOOKAHEAD && strm.avail_in !== 0);
  		}

  		// Copy without compression as much as possible from the input stream,
  		// return
  		// the current block state.
  		// This function does not insert new strings in the dictionary since
  		// uncompressible data is probably not useful. This function is used
  		// only for the level=0 compression option.
  		// NOTE: this function should be optimized to avoid extra copying from
  		// window to pending_buf.
  		function deflate_stored(flush) {
  			// Stored blocks are limited to 0xffff bytes, pending_buf is limited
  			// to pending_buf_size, and each stored block has a 5 byte header:

  			var max_block_size = 0xffff;
  			var max_start;

  			if (max_block_size > pending_buf_size - 5) {
  				max_block_size = pending_buf_size - 5;
  			}

  			// Copy as much as possible from input to output:
  			while (true) {
  				// Fill the window as much as possible:
  				if (lookahead <= 1) {
  					fill_window();
  					if (lookahead === 0 && flush == Z_NO_FLUSH)
  						return NeedMore;
  					if (lookahead === 0)
  						break; // flush the current block
  				}

  				strstart += lookahead;
  				lookahead = 0;

  				// Emit a stored block if pending_buf will be full:
  				max_start = block_start + max_block_size;
  				if (strstart === 0 || strstart >= max_start) {
  					// strstart === 0 is possible when wraparound on 16-bit machine
  					lookahead = (strstart - max_start);
  					strstart = max_start;

  					flush_block_only(false);
  					if (strm.avail_out === 0)
  						return NeedMore;

  				}

  				// Flush if we may have to slide, otherwise block_start may become
  				// negative and the data will be gone:
  				if (strstart - block_start >= w_size - MIN_LOOKAHEAD) {
  					flush_block_only(false);
  					if (strm.avail_out === 0)
  						return NeedMore;
  				}
  			}

  			flush_block_only(flush == Z_FINISH);
  			if (strm.avail_out === 0)
  				return (flush == Z_FINISH) ? FinishStarted : NeedMore;

  			return flush == Z_FINISH ? FinishDone : BlockDone;
  		}

  		function longest_match(cur_match) {
  			var chain_length = max_chain_length; // max hash chain length
  			var scan = strstart; // current string
  			var match; // matched string
  			var len; // length of current match
  			var best_len = prev_length; // best match length so far
  			var limit = strstart > (w_size - MIN_LOOKAHEAD) ? strstart - (w_size - MIN_LOOKAHEAD) : 0;
  			var _nice_match = nice_match;

  			// Stop when cur_match becomes <= limit. To simplify the code,
  			// we prevent matches with the string of window index 0.

  			var wmask = w_mask;

  			var strend = strstart + MAX_MATCH;
  			var scan_end1 = window[scan + best_len - 1];
  			var scan_end = window[scan + best_len];

  			// The code is optimized for HASH_BITS >= 8 and MAX_MATCH-2 multiple of
  			// 16.
  			// It is easy to get rid of this optimization if necessary.

  			// Do not waste too much time if we already have a good match:
  			if (prev_length >= good_match) {
  				chain_length >>= 2;
  			}

  			// Do not look for matches beyond the end of the input. This is
  			// necessary
  			// to make deflate deterministic.
  			if (_nice_match > lookahead)
  				_nice_match = lookahead;

  			do {
  				match = cur_match;

  				// Skip to next match if the match length cannot increase
  				// or if the match length is less than 2:
  				if (window[match + best_len] != scan_end || window[match + best_len - 1] != scan_end1 || window[match] != window[scan]
  						|| window[++match] != window[scan + 1])
  					continue;

  				// The check at best_len-1 can be removed because it will be made
  				// again later. (This heuristic is not always a win.)
  				// It is not necessary to compare scan[2] and match[2] since they
  				// are always equal when the other bytes match, given that
  				// the hash keys are equal and that HASH_BITS >= 8.
  				scan += 2;
  				match++;

  				// We check for insufficient lookahead only every 8th comparison;
  				// the 256th check will be made at strstart+258.
  				do {
  				} while (window[++scan] == window[++match] && window[++scan] == window[++match] && window[++scan] == window[++match]
  						&& window[++scan] == window[++match] && window[++scan] == window[++match] && window[++scan] == window[++match]
  						&& window[++scan] == window[++match] && window[++scan] == window[++match] && scan < strend);

  				len = MAX_MATCH - (strend - scan);
  				scan = strend - MAX_MATCH;

  				if (len > best_len) {
  					match_start = cur_match;
  					best_len = len;
  					if (len >= _nice_match)
  						break;
  					scan_end1 = window[scan + best_len - 1];
  					scan_end = window[scan + best_len];
  				}

  			} while ((cur_match = (prev[cur_match & wmask] & 0xffff)) > limit && --chain_length !== 0);

  			if (best_len <= lookahead)
  				return best_len;
  			return lookahead;
  		}

  		// Compress as much as possible from the input stream, return the current
  		// block state.
  		// This function does not perform lazy evaluation of matches and inserts
  		// new strings in the dictionary only for unmatched strings or for short
  		// matches. It is used only for the fast compression options.
  		function deflate_fast(flush) {
  			// short hash_head = 0; // head of the hash chain
  			var hash_head = 0; // head of the hash chain
  			var bflush; // set if current block must be flushed

  			while (true) {
  				// Make sure that we always have enough lookahead, except
  				// at the end of the input file. We need MAX_MATCH bytes
  				// for the next match, plus MIN_MATCH bytes to insert the
  				// string following the next match.
  				if (lookahead < MIN_LOOKAHEAD) {
  					fill_window();
  					if (lookahead < MIN_LOOKAHEAD && flush == Z_NO_FLUSH) {
  						return NeedMore;
  					}
  					if (lookahead === 0)
  						break; // flush the current block
  				}

  				// Insert the string window[strstart .. strstart+2] in the
  				// dictionary, and set hash_head to the head of the hash chain:
  				if (lookahead >= MIN_MATCH) {
  					ins_h = (((ins_h) << hash_shift) ^ (window[(strstart) + (MIN_MATCH - 1)] & 0xff)) & hash_mask;

  					// prev[strstart&w_mask]=hash_head=head[ins_h];
  					hash_head = (head[ins_h] & 0xffff);
  					prev[strstart & w_mask] = head[ins_h];
  					head[ins_h] = strstart;
  				}

  				// Find the longest match, discarding those <= prev_length.
  				// At this point we have always match_length < MIN_MATCH

  				if (hash_head !== 0 && ((strstart - hash_head) & 0xffff) <= w_size - MIN_LOOKAHEAD) {
  					// To simplify the code, we prevent matches with the string
  					// of window index 0 (in particular we have to avoid a match
  					// of the string with itself at the start of the input file).
  					if (strategy != Z_HUFFMAN_ONLY) {
  						match_length = longest_match(hash_head);
  					}
  					// longest_match() sets match_start
  				}
  				if (match_length >= MIN_MATCH) {
  					// check_match(strstart, match_start, match_length);

  					bflush = _tr_tally(strstart - match_start, match_length - MIN_MATCH);

  					lookahead -= match_length;

  					// Insert new strings in the hash table only if the match length
  					// is not too large. This saves time but degrades compression.
  					if (match_length <= max_lazy_match && lookahead >= MIN_MATCH) {
  						match_length--; // string at strstart already in hash table
  						do {
  							strstart++;

  							ins_h = ((ins_h << hash_shift) ^ (window[(strstart) + (MIN_MATCH - 1)] & 0xff)) & hash_mask;
  							// prev[strstart&w_mask]=hash_head=head[ins_h];
  							hash_head = (head[ins_h] & 0xffff);
  							prev[strstart & w_mask] = head[ins_h];
  							head[ins_h] = strstart;

  							// strstart never exceeds WSIZE-MAX_MATCH, so there are
  							// always MIN_MATCH bytes ahead.
  						} while (--match_length !== 0);
  						strstart++;
  					} else {
  						strstart += match_length;
  						match_length = 0;
  						ins_h = window[strstart] & 0xff;

  						ins_h = (((ins_h) << hash_shift) ^ (window[strstart + 1] & 0xff)) & hash_mask;
  						// If lookahead < MIN_MATCH, ins_h is garbage, but it does
  						// not
  						// matter since it will be recomputed at next deflate call.
  					}
  				} else {
  					// No match, output a literal byte

  					bflush = _tr_tally(0, window[strstart] & 0xff);
  					lookahead--;
  					strstart++;
  				}
  				if (bflush) {

  					flush_block_only(false);
  					if (strm.avail_out === 0)
  						return NeedMore;
  				}
  			}

  			flush_block_only(flush == Z_FINISH);
  			if (strm.avail_out === 0) {
  				if (flush == Z_FINISH)
  					return FinishStarted;
  				else
  					return NeedMore;
  			}
  			return flush == Z_FINISH ? FinishDone : BlockDone;
  		}

  		// Same as above, but achieves better compression. We use a lazy
  		// evaluation for matches: a match is finally adopted only if there is
  		// no better match at the next window position.
  		function deflate_slow(flush) {
  			// short hash_head = 0; // head of hash chain
  			var hash_head = 0; // head of hash chain
  			var bflush; // set if current block must be flushed
  			var max_insert;

  			// Process the input block.
  			while (true) {
  				// Make sure that we always have enough lookahead, except
  				// at the end of the input file. We need MAX_MATCH bytes
  				// for the next match, plus MIN_MATCH bytes to insert the
  				// string following the next match.

  				if (lookahead < MIN_LOOKAHEAD) {
  					fill_window();
  					if (lookahead < MIN_LOOKAHEAD && flush == Z_NO_FLUSH) {
  						return NeedMore;
  					}
  					if (lookahead === 0)
  						break; // flush the current block
  				}

  				// Insert the string window[strstart .. strstart+2] in the
  				// dictionary, and set hash_head to the head of the hash chain:

  				if (lookahead >= MIN_MATCH) {
  					ins_h = (((ins_h) << hash_shift) ^ (window[(strstart) + (MIN_MATCH - 1)] & 0xff)) & hash_mask;
  					// prev[strstart&w_mask]=hash_head=head[ins_h];
  					hash_head = (head[ins_h] & 0xffff);
  					prev[strstart & w_mask] = head[ins_h];
  					head[ins_h] = strstart;
  				}

  				// Find the longest match, discarding those <= prev_length.
  				prev_length = match_length;
  				prev_match = match_start;
  				match_length = MIN_MATCH - 1;

  				if (hash_head !== 0 && prev_length < max_lazy_match && ((strstart - hash_head) & 0xffff) <= w_size - MIN_LOOKAHEAD) {
  					// To simplify the code, we prevent matches with the string
  					// of window index 0 (in particular we have to avoid a match
  					// of the string with itself at the start of the input file).

  					if (strategy != Z_HUFFMAN_ONLY) {
  						match_length = longest_match(hash_head);
  					}
  					// longest_match() sets match_start

  					if (match_length <= 5 && (strategy == Z_FILTERED || (match_length == MIN_MATCH && strstart - match_start > 4096))) {

  						// If prev_match is also MIN_MATCH, match_start is garbage
  						// but we will ignore the current match anyway.
  						match_length = MIN_MATCH - 1;
  					}
  				}

  				// If there was a match at the previous step and the current
  				// match is not better, output the previous match:
  				if (prev_length >= MIN_MATCH && match_length <= prev_length) {
  					max_insert = strstart + lookahead - MIN_MATCH;
  					// Do not insert strings in hash table beyond this.

  					// check_match(strstart-1, prev_match, prev_length);

  					bflush = _tr_tally(strstart - 1 - prev_match, prev_length - MIN_MATCH);

  					// Insert in hash table all strings up to the end of the match.
  					// strstart-1 and strstart are already inserted. If there is not
  					// enough lookahead, the last two strings are not inserted in
  					// the hash table.
  					lookahead -= prev_length - 1;
  					prev_length -= 2;
  					do {
  						if (++strstart <= max_insert) {
  							ins_h = (((ins_h) << hash_shift) ^ (window[(strstart) + (MIN_MATCH - 1)] & 0xff)) & hash_mask;
  							// prev[strstart&w_mask]=hash_head=head[ins_h];
  							hash_head = (head[ins_h] & 0xffff);
  							prev[strstart & w_mask] = head[ins_h];
  							head[ins_h] = strstart;
  						}
  					} while (--prev_length !== 0);
  					match_available = 0;
  					match_length = MIN_MATCH - 1;
  					strstart++;

  					if (bflush) {
  						flush_block_only(false);
  						if (strm.avail_out === 0)
  							return NeedMore;
  					}
  				} else if (match_available !== 0) {

  					// If there was no match at the previous position, output a
  					// single literal. If there was a match but the current match
  					// is longer, truncate the previous match to a single literal.

  					bflush = _tr_tally(0, window[strstart - 1] & 0xff);

  					if (bflush) {
  						flush_block_only(false);
  					}
  					strstart++;
  					lookahead--;
  					if (strm.avail_out === 0)
  						return NeedMore;
  				} else {
  					// There is no previous match to compare with, wait for
  					// the next step to decide.

  					match_available = 1;
  					strstart++;
  					lookahead--;
  				}
  			}

  			if (match_available !== 0) {
  				bflush = _tr_tally(0, window[strstart - 1] & 0xff);
  				match_available = 0;
  			}
  			flush_block_only(flush == Z_FINISH);

  			if (strm.avail_out === 0) {
  				if (flush == Z_FINISH)
  					return FinishStarted;
  				else
  					return NeedMore;
  			}

  			return flush == Z_FINISH ? FinishDone : BlockDone;
  		}

  		function deflateReset(strm) {
  			strm.total_in = strm.total_out = 0;
  			strm.msg = null; //
  			
  			that.pending = 0;
  			that.pending_out = 0;

  			status = BUSY_STATE;

  			last_flush = Z_NO_FLUSH;

  			tr_init();
  			lm_init();
  			return Z_OK;
  		}

  		that.deflateInit = function(strm, _level, bits, _method, memLevel, _strategy) {
  			if (!_method)
  				_method = Z_DEFLATED;
  			if (!memLevel)
  				memLevel = DEF_MEM_LEVEL;
  			if (!_strategy)
  				_strategy = Z_DEFAULT_STRATEGY;

  			// byte[] my_version=ZLIB_VERSION;

  			//
  			// if (!version || version[0] != my_version[0]
  			// || stream_size != sizeof(z_stream)) {
  			// return Z_VERSION_ERROR;
  			// }

  			strm.msg = null;

  			if (_level == Z_DEFAULT_COMPRESSION)
  				_level = 6;

  			if (memLevel < 1 || memLevel > MAX_MEM_LEVEL || _method != Z_DEFLATED || bits < 9 || bits > 15 || _level < 0 || _level > 9 || _strategy < 0
  					|| _strategy > Z_HUFFMAN_ONLY) {
  				return Z_STREAM_ERROR;
  			}

  			strm.dstate = that;

  			w_bits = bits;
  			w_size = 1 << w_bits;
  			w_mask = w_size - 1;

  			hash_bits = memLevel + 7;
  			hash_size = 1 << hash_bits;
  			hash_mask = hash_size - 1;
  			hash_shift = Math.floor((hash_bits + MIN_MATCH - 1) / MIN_MATCH);

  			window = new Uint8Array(w_size * 2);
  			prev = [];
  			head = [];

  			lit_bufsize = 1 << (memLevel + 6); // 16K elements by default

  			// We overlay pending_buf and d_buf+l_buf. This works since the average
  			// output size for (length,distance) codes is <= 24 bits.
  			that.pending_buf = new Uint8Array(lit_bufsize * 4);
  			pending_buf_size = lit_bufsize * 4;

  			d_buf = Math.floor(lit_bufsize / 2);
  			l_buf = (1 + 2) * lit_bufsize;

  			level = _level;

  			strategy = _strategy;
  			method = _method & 0xff;

  			return deflateReset(strm);
  		};

  		that.deflateEnd = function() {
  			if (status != INIT_STATE && status != BUSY_STATE && status != FINISH_STATE) {
  				return Z_STREAM_ERROR;
  			}
  			// Deallocate in reverse order of allocations:
  			that.pending_buf = null;
  			head = null;
  			prev = null;
  			window = null;
  			// free
  			that.dstate = null;
  			return status == BUSY_STATE ? Z_DATA_ERROR : Z_OK;
  		};

  		that.deflateParams = function(strm, _level, _strategy) {
  			var err = Z_OK;

  			if (_level == Z_DEFAULT_COMPRESSION) {
  				_level = 6;
  			}
  			if (_level < 0 || _level > 9 || _strategy < 0 || _strategy > Z_HUFFMAN_ONLY) {
  				return Z_STREAM_ERROR;
  			}

  			if (config_table[level].func != config_table[_level].func && strm.total_in !== 0) {
  				// Flush the last buffer:
  				err = strm.deflate(Z_PARTIAL_FLUSH);
  			}

  			if (level != _level) {
  				level = _level;
  				max_lazy_match = config_table[level].max_lazy;
  				good_match = config_table[level].good_length;
  				nice_match = config_table[level].nice_length;
  				max_chain_length = config_table[level].max_chain;
  			}
  			strategy = _strategy;
  			return err;
  		};

  		that.deflateSetDictionary = function(strm, dictionary, dictLength) {
  			var length = dictLength;
  			var n, index = 0;

  			if (!dictionary || status != INIT_STATE)
  				return Z_STREAM_ERROR;

  			if (length < MIN_MATCH)
  				return Z_OK;
  			if (length > w_size - MIN_LOOKAHEAD) {
  				length = w_size - MIN_LOOKAHEAD;
  				index = dictLength - length; // use the tail of the dictionary
  			}
  			window.set(dictionary.subarray(index, index + length), 0);

  			strstart = length;
  			block_start = length;

  			// Insert all strings in the hash table (except for the last two bytes).
  			// s->lookahead stays null, so s->ins_h will be recomputed at the next
  			// call of fill_window.

  			ins_h = window[0] & 0xff;
  			ins_h = (((ins_h) << hash_shift) ^ (window[1] & 0xff)) & hash_mask;

  			for (n = 0; n <= length - MIN_MATCH; n++) {
  				ins_h = (((ins_h) << hash_shift) ^ (window[(n) + (MIN_MATCH - 1)] & 0xff)) & hash_mask;
  				prev[n & w_mask] = head[ins_h];
  				head[ins_h] = n;
  			}
  			return Z_OK;
  		};

  		that.deflate = function(_strm, flush) {
  			var i, header, level_flags, old_flush, bstate;

  			if (flush > Z_FINISH || flush < 0) {
  				return Z_STREAM_ERROR;
  			}

  			if (!_strm.next_out || (!_strm.next_in && _strm.avail_in !== 0) || (status == FINISH_STATE && flush != Z_FINISH)) {
  				_strm.msg = z_errmsg[Z_NEED_DICT - (Z_STREAM_ERROR)];
  				return Z_STREAM_ERROR;
  			}
  			if (_strm.avail_out === 0) {
  				_strm.msg = z_errmsg[Z_NEED_DICT - (Z_BUF_ERROR)];
  				return Z_BUF_ERROR;
  			}

  			strm = _strm; // just in case
  			old_flush = last_flush;
  			last_flush = flush;

  			// Write the zlib header
  			if (status == INIT_STATE) {
  				header = (Z_DEFLATED + ((w_bits - 8) << 4)) << 8;
  				level_flags = ((level - 1) & 0xff) >> 1;

  				if (level_flags > 3)
  					level_flags = 3;
  				header |= (level_flags << 6);
  				if (strstart !== 0)
  					header |= PRESET_DICT;
  				header += 31 - (header % 31);

  				status = BUSY_STATE;
  				putShortMSB(header);
  			}

  			// Flush as much pending output as possible
  			if (that.pending !== 0) {
  				strm.flush_pending();
  				if (strm.avail_out === 0) {
  					// console.log(" avail_out==0");
  					// Since avail_out is 0, deflate will be called again with
  					// more output space, but possibly with both pending and
  					// avail_in equal to zero. There won't be anything to do,
  					// but this is not an error situation so make sure we
  					// return OK instead of BUF_ERROR at next call of deflate:
  					last_flush = -1;
  					return Z_OK;
  				}

  				// Make sure there is something to do and avoid duplicate
  				// consecutive
  				// flushes. For repeated and useless calls with Z_FINISH, we keep
  				// returning Z_STREAM_END instead of Z_BUFF_ERROR.
  			} else if (strm.avail_in === 0 && flush <= old_flush && flush != Z_FINISH) {
  				strm.msg = z_errmsg[Z_NEED_DICT - (Z_BUF_ERROR)];
  				return Z_BUF_ERROR;
  			}

  			// User must not provide more input after the first FINISH:
  			if (status == FINISH_STATE && strm.avail_in !== 0) {
  				_strm.msg = z_errmsg[Z_NEED_DICT - (Z_BUF_ERROR)];
  				return Z_BUF_ERROR;
  			}

  			// Start a new block or continue the current one.
  			if (strm.avail_in !== 0 || lookahead !== 0 || (flush != Z_NO_FLUSH && status != FINISH_STATE)) {
  				bstate = -1;
  				switch (config_table[level].func) {
  				case STORED:
  					bstate = deflate_stored(flush);
  					break;
  				case FAST:
  					bstate = deflate_fast(flush);
  					break;
  				case SLOW:
  					bstate = deflate_slow(flush);
  					break;
  				default:
  				}

  				if (bstate == FinishStarted || bstate == FinishDone) {
  					status = FINISH_STATE;
  				}
  				if (bstate == NeedMore || bstate == FinishStarted) {
  					if (strm.avail_out === 0) {
  						last_flush = -1; // avoid BUF_ERROR next call, see above
  					}
  					return Z_OK;
  					// If flush != Z_NO_FLUSH && avail_out === 0, the next call
  					// of deflate should use the same flush parameter to make sure
  					// that the flush is complete. So we don't have to output an
  					// empty block here, this will be done at next call. This also
  					// ensures that for a very small output buffer, we emit at most
  					// one empty block.
  				}

  				if (bstate == BlockDone) {
  					if (flush == Z_PARTIAL_FLUSH) {
  						_tr_align();
  					} else { // FULL_FLUSH or SYNC_FLUSH
  						_tr_stored_block(0, 0, false);
  						// For a full flush, this empty block will be recognized
  						// as a special marker by inflate_sync().
  						if (flush == Z_FULL_FLUSH) {
  							// state.head[s.hash_size-1]=0;
  							for (i = 0; i < hash_size/*-1*/; i++)
  								// forget history
  								head[i] = 0;
  						}
  					}
  					strm.flush_pending();
  					if (strm.avail_out === 0) {
  						last_flush = -1; // avoid BUF_ERROR at next call, see above
  						return Z_OK;
  					}
  				}
  			}

  			if (flush != Z_FINISH)
  				return Z_OK;
  			return Z_STREAM_END;
  		};
  	}

  	// ZStream

  	function ZStream() {
  		var that = this;
  		that.next_in_index = 0;
  		that.next_out_index = 0;
  		// that.next_in; // next input byte
  		that.avail_in = 0; // number of bytes available at next_in
  		that.total_in = 0; // total nb of input bytes read so far
  		// that.next_out; // next output byte should be put there
  		that.avail_out = 0; // remaining free space at next_out
  		that.total_out = 0; // total nb of bytes output so far
  		// that.msg;
  		// that.dstate;
  	}

  	ZStream.prototype = {
  		deflateInit : function(level, bits) {
  			var that = this;
  			that.dstate = new Deflate();
  			if (!bits)
  				bits = MAX_BITS;
  			return that.dstate.deflateInit(that, level, bits);
  		},

  		deflate : function(flush) {
  			var that = this;
  			if (!that.dstate) {
  				return Z_STREAM_ERROR;
  			}
  			return that.dstate.deflate(that, flush);
  		},

  		deflateEnd : function() {
  			var that = this;
  			if (!that.dstate)
  				return Z_STREAM_ERROR;
  			var ret = that.dstate.deflateEnd();
  			that.dstate = null;
  			return ret;
  		},

  		deflateParams : function(level, strategy) {
  			var that = this;
  			if (!that.dstate)
  				return Z_STREAM_ERROR;
  			return that.dstate.deflateParams(that, level, strategy);
  		},

  		deflateSetDictionary : function(dictionary, dictLength) {
  			var that = this;
  			if (!that.dstate)
  				return Z_STREAM_ERROR;
  			return that.dstate.deflateSetDictionary(that, dictionary, dictLength);
  		},

  		// Read a new buffer from the current input stream, update the
  		// total number of bytes read. All deflate() input goes through
  		// this function so some applications may wish to modify it to avoid
  		// allocating a large strm->next_in buffer and copying from it.
  		// (See also flush_pending()).
  		read_buf : function(buf, start, size) {
  			var that = this;
  			var len = that.avail_in;
  			if (len > size)
  				len = size;
  			if (len === 0)
  				return 0;
  			that.avail_in -= len;
  			buf.set(that.next_in.subarray(that.next_in_index, that.next_in_index + len), start);
  			that.next_in_index += len;
  			that.total_in += len;
  			return len;
  		},

  		// Flush as much pending output as possible. All deflate() output goes
  		// through this function so some applications may wish to modify it
  		// to avoid allocating a large strm->next_out buffer and copying into it.
  		// (See also read_buf()).
  		flush_pending : function() {
  			var that = this;
  			var len = that.dstate.pending;

  			if (len > that.avail_out)
  				len = that.avail_out;
  			if (len === 0)
  				return;

  			// if (that.dstate.pending_buf.length <= that.dstate.pending_out || that.next_out.length <= that.next_out_index
  			// || that.dstate.pending_buf.length < (that.dstate.pending_out + len) || that.next_out.length < (that.next_out_index +
  			// len)) {
  			// console.log(that.dstate.pending_buf.length + ", " + that.dstate.pending_out + ", " + that.next_out.length + ", " +
  			// that.next_out_index + ", " + len);
  			// console.log("avail_out=" + that.avail_out);
  			// }

  			that.next_out.set(that.dstate.pending_buf.subarray(that.dstate.pending_out, that.dstate.pending_out + len), that.next_out_index);

  			that.next_out_index += len;
  			that.dstate.pending_out += len;
  			that.total_out += len;
  			that.avail_out -= len;
  			that.dstate.pending -= len;
  			if (that.dstate.pending === 0) {
  				that.dstate.pending_out = 0;
  			}
  		}
  	};

  	// Deflater

  	return function Deflater(level) {
  		var that = this;
  		var z = new ZStream();
  		var bufsize = 512;
  		var flush = Z_NO_FLUSH;
  		var buf = new Uint8Array(bufsize);

  		if (typeof level == "undefined")
  			level = Z_DEFAULT_COMPRESSION;
  		z.deflateInit(level);
  		z.next_out = buf;

  		that.append = function(data, onprogress) {
  			var err, buffers = [], lastIndex = 0, bufferIndex = 0, bufferSize = 0, array;
  			if (!data.length)
  				return;
  			z.next_in_index = 0;
  			z.next_in = data;
  			z.avail_in = data.length;
  			do {
  				z.next_out_index = 0;
  				z.avail_out = bufsize;
  				err = z.deflate(flush);
  				if (err != Z_OK)
  					throw "deflating: " + z.msg;
  				if (z.next_out_index)
  					if (z.next_out_index == bufsize)
  						buffers.push(new Uint8Array(buf));
  					else
  						buffers.push(new Uint8Array(buf.subarray(0, z.next_out_index)));
  				bufferSize += z.next_out_index;
  				if (onprogress && z.next_in_index > 0 && z.next_in_index != lastIndex) {
  					onprogress(z.next_in_index);
  					lastIndex = z.next_in_index;
  				}
  			} while (z.avail_in > 0 || z.avail_out === 0);
  			array = new Uint8Array(bufferSize);
  			buffers.forEach(function(chunk) {
  				array.set(chunk, bufferIndex);
  				bufferIndex += chunk.length;
  			});
  			return array;
  		};
  		that.flush = function() {
  			var err, buffers = [], bufferIndex = 0, bufferSize = 0, array;
  			do {
  				z.next_out_index = 0;
  				z.avail_out = bufsize;
  				err = z.deflate(Z_FINISH);
  				if (err != Z_STREAM_END && err != Z_OK)
  					throw "deflating: " + z.msg;
  				if (bufsize - z.avail_out > 0)
  					buffers.push(new Uint8Array(buf.subarray(0, z.next_out_index)));
  				bufferSize += z.next_out_index;
  			} while (z.avail_in > 0 || z.avail_out === 0);
  			z.deflateEnd();
  			array = new Uint8Array(bufferSize);
  			buffers.forEach(function(chunk) {
  				array.set(chunk, bufferIndex);
  				bufferIndex += chunk.length;
  			});
  			return array;
  		};
  	};
  })(undefined);
  

  /*
    html2canvas 0.5.0-alpha <http://html2canvas.hertzen.com>
    Copyright (c) 2014 Niklas von Hertzen

    Released under MIT License
  */

  (function(window, document, module, exports, global, define, undefined){

  /*
   Copyright (c) 2013 Yehuda Katz, Tom Dale, and contributors

   
   */
  !function(){var a,b,c,d;!function(){var e={},f={};a=function(a,b,c){e[a]={deps:b,callback:c}},d=c=b=function(a){function c(b){if("."!==b.charAt(0))return b;for(var c=b.split("/"),d=a.split("/").slice(0,-1),e=0,f=c.length;f>e;e++){var g=c[e];if(".."===g)d.pop();else{if("."===g)continue;d.push(g)}}return d.join("/")}if(d._eak_seen=e,f[a])return f[a];if(f[a]={},!e[a])throw new Error("Could not find module "+a);for(var g,h=e[a],i=h.deps,j=h.callback,k=[],l=0,m=i.length;m>l;l++)"exports"===i[l]?k.push(g={}):k.push(b(c(i[l])));var n=j.apply(this,k);return f[a]=g||n}}(),a("promise/all",["./utils","exports"],function(a,b){"use strict";function c(a){var b=this;if(!d(a))throw new TypeError("You must pass an array to all.");return new b(function(b,c){function d(a){return function(b){f(a,b)}}function f(a,c){h[a]=c,0===--i&&b(h)}var g,h=[],i=a.length;0===i&&b([]);for(var j=0;j<a.length;j++)g=a[j],g&&e(g.then)?g.then(d(j),c):f(j,g)})}var d=a.isArray,e=a.isFunction;b.all=c}),a("promise/asap",["exports"],function(a){"use strict";function b(){return function(){process.nextTick(e)}}function c(){var a=0,b=new i(e),c=document.createTextNode("");return b.observe(c,{characterData:!0}),function(){c.data=a=++a%2}}function d(){return function(){j.setTimeout(e,1)}}function e(){for(var a=0;a<k.length;a++){var b=k[a],c=b[0],d=b[1];c(d)}k=[]}function f(a,b){var c=k.push([a,b]);1===c&&g()}var g,h="undefined"!=typeof window?window:{},i=h.MutationObserver||h.WebKitMutationObserver,j="undefined"!=typeof global?global:this,k=[];g="undefined"!=typeof process&&"[object process]"==={}.toString.call(process)?b():i?c():d(),a.asap=f}),a("promise/cast",["exports"],function(a){"use strict";function b(a){if(a&&"object"==typeof a&&a.constructor===this)return a;var b=this;return new b(function(b){b(a)})}a.cast=b}),a("promise/config",["exports"],function(a){"use strict";function b(a,b){return 2!==arguments.length?c[a]:(c[a]=b,void 0)}var c={instrument:!1};a.config=c,a.configure=b}),a("promise/polyfill",["./promise","./utils","exports"],function(a,b,c){"use strict";function d(){var a="Promise"in window&&"cast"in window.Promise&&"resolve"in window.Promise&&"reject"in window.Promise&&"all"in window.Promise&&"race"in window.Promise&&function(){var a;return new window.Promise(function(b){a=b}),f(a)}();a||(window.Promise=e)}var e=a.Promise,f=b.isFunction;c.polyfill=d}),a("promise/promise",["./config","./utils","./cast","./all","./race","./resolve","./reject","./asap","exports"],function(a,b,c,d,e,f,g,h,i){"use strict";function j(a){if(!w(a))throw new TypeError("You must pass a resolver function as the first argument to the promise constructor");if(!(this instanceof j))throw new TypeError("Failed to construct 'Promise': Please use the 'new' operator, this object constructor cannot be called as a function.");this._subscribers=[],k(a,this)}function k(a,b){function c(a){p(b,a)}function d(a){r(b,a)}try{a(c,d)}catch(e){d(e)}}function l(a,b,c,d){var e,f,g,h,i=w(c);if(i)try{e=c(d),g=!0}catch(j){h=!0,f=j}else e=d,g=!0;o(b,e)||(i&&g?p(b,e):h?r(b,f):a===F?p(b,e):a===G&&r(b,e))}function m(a,b,c,d){var e=a._subscribers,f=e.length;e[f]=b,e[f+F]=c,e[f+G]=d}function n(a,b){for(var c,d,e=a._subscribers,f=a._detail,g=0;g<e.length;g+=3)c=e[g],d=e[g+b],l(b,c,d,f);a._subscribers=null}function o(a,b){var c,d=null;try{if(a===b)throw new TypeError("A promises callback cannot return that same promise.");if(v(b)&&(d=b.then,w(d)))return d.call(b,function(d){return c?!0:(c=!0,b!==d?p(a,d):q(a,d),void 0)},function(b){return c?!0:(c=!0,r(a,b),void 0)}),!0}catch(e){return c?!0:(r(a,e),!0)}return!1}function p(a,b){a===b?q(a,b):o(a,b)||q(a,b)}function q(a,b){a._state===D&&(a._state=E,a._detail=b,u.async(s,a))}function r(a,b){a._state===D&&(a._state=E,a._detail=b,u.async(t,a))}function s(a){n(a,a._state=F)}function t(a){n(a,a._state=G)}var u=a.config,v=(a.configure,b.objectOrFunction),w=b.isFunction,x=(b.now,c.cast),y=d.all,z=e.race,A=f.resolve,B=g.reject,C=h.asap;u.async=C;var D=void 0,E=0,F=1,G=2;j.prototype={constructor:j,_state:void 0,_detail:void 0,_subscribers:void 0,then:function(a,b){var c=this,d=new this.constructor(function(){});if(this._state){var e=arguments;u.async(function(){l(c._state,d,e[c._state-1],c._detail)})}else m(this,d,a,b);return d},"catch":function(a){return this.then(null,a)}},j.all=y,j.cast=x,j.race=z,j.resolve=A,j.reject=B,i.Promise=j}),a("promise/race",["./utils","exports"],function(a,b){"use strict";function c(a){var b=this;if(!d(a))throw new TypeError("You must pass an array to race.");return new b(function(b,c){for(var d,e=0;e<a.length;e++)d=a[e],d&&"function"==typeof d.then?d.then(b,c):b(d)})}var d=a.isArray;b.race=c}),a("promise/reject",["exports"],function(a){"use strict";function b(a){var b=this;return new b(function(b,c){c(a)})}a.reject=b}),a("promise/resolve",["exports"],function(a){"use strict";function b(a){var b=this;return new b(function(b){b(a)})}a.resolve=b}),a("promise/utils",["exports"],function(a){"use strict";function b(a){return c(a)||"object"==typeof a&&null!==a}function c(a){return"function"==typeof a}function d(a){return"[object Array]"===Object.prototype.toString.call(a)}var e=Date.now||function(){return(new Date).getTime()};a.objectOrFunction=b,a.isFunction=c,a.isArray=d,a.now=e}),b("promise/polyfill").polyfill()}();

  if (typeof(Object.create) !== "function" || typeof(document.createElement("canvas").getContext) !== "function") {
      window.html2canvas = function() {
          return Promise.reject("No canvas support");
      };
      return;
  }

  /*! https://mths.be/punycode v1.3.1 by @mathias */
  ;(function(root) {

  	/** Detect free variables */
  	var freeExports = typeof exports == 'object' && exports &&
  		!exports.nodeType && exports;
  	var freeModule = typeof module == 'object' && module &&
  		!module.nodeType && module;
  	var freeGlobal = typeof global == 'object' && global;
  	if (
  		freeGlobal.global === freeGlobal ||
  		freeGlobal.window === freeGlobal ||
  		freeGlobal.self === freeGlobal
  	) {
  		root = freeGlobal;
  	}

  	/**
  	 * The `punycode` object.
  	 * @name punycode
  	 * @type Object
  	 */
  	var punycode,

  	/** Highest positive signed 32-bit float value */
  	maxInt = 2147483647, // aka. 0x7FFFFFFF or 2^31-1

  	/** Bootstring parameters */
  	base = 36,
  	tMin = 1,
  	tMax = 26,
  	skew = 38,
  	damp = 700,
  	initialBias = 72,
  	initialN = 128, // 0x80
  	delimiter = '-', // '\x2D'

  	/** Regular expressions */
  	regexPunycode = /^xn--/,
  	regexNonASCII = /[^\x20-\x7E]/, // unprintable ASCII chars + non-ASCII chars
  	regexSeparators = /[\x2E\u3002\uFF0E\uFF61]/g, // RFC 3490 separators

  	/** Error messages */
  	errors = {
  		'overflow': 'Overflow: input needs wider integers to process',
  		'not-basic': 'Illegal input >= 0x80 (not a basic code point)',
  		'invalid-input': 'Invalid input'
  	},

  	/** Convenience shortcuts */
  	baseMinusTMin = base - tMin,
  	floor = Math.floor,
  	stringFromCharCode = String.fromCharCode,

  	/** Temporary variable */
  	key;

  	/*--------------------------------------------------------------------------*/

  	/**
  	 * A generic error utility function.
  	 * @private
  	 * @param {String} type The error type.
  	 * @returns {Error} Throws a `RangeError` with the applicable error message.
  	 */
  	function error(type) {
  		throw RangeError(errors[type]);
  	}

  	/**
  	 * A generic `Array#map` utility function.
  	 * @private
  	 * @param {Array} array The array to iterate over.
  	 * @param {Function} callback The function that gets called for every array
  	 * item.
  	 * @returns {Array} A new array of values returned by the callback function.
  	 */
  	function map(array, fn) {
  		var length = array.length;
  		var result = [];
  		while (length--) {
  			result[length] = fn(array[length]);
  		}
  		return result;
  	}

  	/**
  	 * A simple `Array#map`-like wrapper to work with domain name strings or email
  	 * addresses.
  	 * @private
  	 * @param {String} domain The domain name or email address.
  	 * @param {Function} callback The function that gets called for every
  	 * character.
  	 * @returns {Array} A new string of characters returned by the callback
  	 * function.
  	 */
  	function mapDomain(string, fn) {
  		var parts = string.split('@');
  		var result = '';
  		if (parts.length > 1) {
  			// In email addresses, only the domain name should be punycoded. Leave
  			// the local part (i.e. everything up to `@`) intact.
  			result = parts[0] + '@';
  			string = parts[1];
  		}
  		var labels = string.split(regexSeparators);
  		var encoded = map(labels, fn).join('.');
  		return result + encoded;
  	}

  	/**
  	 * Creates an array containing the numeric code points of each Unicode
  	 * character in the string. While JavaScript uses UCS-2 internally,
  	 * this function will convert a pair of surrogate halves (each of which
  	 * UCS-2 exposes as separate characters) into a single code point,
  	 * matching UTF-16.
  	 * @see `punycode.ucs2.encode`
  	 * @see <https://mathiasbynens.be/notes/javascript-encoding>
  	 * @memberOf punycode.ucs2
  	 * @name decode
  	 * @param {String} string The Unicode input string (UCS-2).
  	 * @returns {Array} The new array of code points.
  	 */
  	function ucs2decode(string) {
  		var output = [],
  		    counter = 0,
  		    length = string.length,
  		    value,
  		    extra;
  		while (counter < length) {
  			value = string.charCodeAt(counter++);
  			if (value >= 0xD800 && value <= 0xDBFF && counter < length) {
  				// high surrogate, and there is a next character
  				extra = string.charCodeAt(counter++);
  				if ((extra & 0xFC00) == 0xDC00) { // low surrogate
  					output.push(((value & 0x3FF) << 10) + (extra & 0x3FF) + 0x10000);
  				} else {
  					// unmatched surrogate; only append this code unit, in case the next
  					// code unit is the high surrogate of a surrogate pair
  					output.push(value);
  					counter--;
  				}
  			} else {
  				output.push(value);
  			}
  		}
  		return output;
  	}

  	/**
  	 * Creates a string based on an array of numeric code points.
  	 * @see `punycode.ucs2.decode`
  	 * @memberOf punycode.ucs2
  	 * @name encode
  	 * @param {Array} codePoints The array of numeric code points.
  	 * @returns {String} The new Unicode string (UCS-2).
  	 */
  	function ucs2encode(array) {
  		return map(array, function(value) {
  			var output = '';
  			if (value > 0xFFFF) {
  				value -= 0x10000;
  				output += stringFromCharCode(value >>> 10 & 0x3FF | 0xD800);
  				value = 0xDC00 | value & 0x3FF;
  			}
  			output += stringFromCharCode(value);
  			return output;
  		}).join('');
  	}

  	/**
  	 * Converts a basic code point into a digit/integer.
  	 * @see `digitToBasic()`
  	 * @private
  	 * @param {Number} codePoint The basic numeric code point value.
  	 * @returns {Number} The numeric value of a basic code point (for use in
  	 * representing integers) in the range `0` to `base - 1`, or `base` if
  	 * the code point does not represent a value.
  	 */
  	function basicToDigit(codePoint) {
  		if (codePoint - 48 < 10) {
  			return codePoint - 22;
  		}
  		if (codePoint - 65 < 26) {
  			return codePoint - 65;
  		}
  		if (codePoint - 97 < 26) {
  			return codePoint - 97;
  		}
  		return base;
  	}

  	/**
  	 * Converts a digit/integer into a basic code point.
  	 * @see `basicToDigit()`
  	 * @private
  	 * @param {Number} digit The numeric value of a basic code point.
  	 * @returns {Number} The basic code point whose value (when used for
  	 * representing integers) is `digit`, which needs to be in the range
  	 * `0` to `base - 1`. If `flag` is non-zero, the uppercase form is
  	 * used; else, the lowercase form is used. The behavior is undefined
  	 * if `flag` is non-zero and `digit` has no uppercase form.
  	 */
  	function digitToBasic(digit, flag) {
  		//  0..25 map to ASCII a..z or A..Z
  		// 26..35 map to ASCII 0..9
  		return digit + 22 + 75 * (digit < 26) - ((flag != 0) << 5);
  	}

  	/**
  	 * Bias adaptation function as per section 3.4 of RFC 3492.
  	 * http://tools.ietf.org/html/rfc3492#section-3.4
  	 * @private
  	 */
  	function adapt(delta, numPoints, firstTime) {
  		var k = 0;
  		delta = firstTime ? floor(delta / damp) : delta >> 1;
  		delta += floor(delta / numPoints);
  		for (/* no initialization */; delta > baseMinusTMin * tMax >> 1; k += base) {
  			delta = floor(delta / baseMinusTMin);
  		}
  		return floor(k + (baseMinusTMin + 1) * delta / (delta + skew));
  	}

  	/**
  	 * Converts a Punycode string of ASCII-only symbols to a string of Unicode
  	 * symbols.
  	 * @memberOf punycode
  	 * @param {String} input The Punycode string of ASCII-only symbols.
  	 * @returns {String} The resulting string of Unicode symbols.
  	 */
  	function decode(input) {
  		// Don't use UCS-2
  		var output = [],
  		    inputLength = input.length,
  		    out,
  		    i = 0,
  		    n = initialN,
  		    bias = initialBias,
  		    basic,
  		    j,
  		    index,
  		    oldi,
  		    w,
  		    k,
  		    digit,
  		    t,
  		    /** Cached calculation results */
  		    baseMinusT;

  		// Handle the basic code points: let `basic` be the number of input code
  		// points before the last delimiter, or `0` if there is none, then copy
  		// the first basic code points to the output.

  		basic = input.lastIndexOf(delimiter);
  		if (basic < 0) {
  			basic = 0;
  		}

  		for (j = 0; j < basic; ++j) {
  			// if it's not a basic code point
  			if (input.charCodeAt(j) >= 0x80) {
  				error('not-basic');
  			}
  			output.push(input.charCodeAt(j));
  		}

  		// Main decoding loop: start just after the last delimiter if any basic code
  		// points were copied; start at the beginning otherwise.

  		for (index = basic > 0 ? basic + 1 : 0; index < inputLength; /* no final expression */) {

  			// `index` is the index of the next character to be consumed.
  			// Decode a generalized variable-length integer into `delta`,
  			// which gets added to `i`. The overflow checking is easier
  			// if we increase `i` as we go, then subtract off its starting
  			// value at the end to obtain `delta`.
  			for (oldi = i, w = 1, k = base; /* no condition */; k += base) {

  				if (index >= inputLength) {
  					error('invalid-input');
  				}

  				digit = basicToDigit(input.charCodeAt(index++));

  				if (digit >= base || digit > floor((maxInt - i) / w)) {
  					error('overflow');
  				}

  				i += digit * w;
  				t = k <= bias ? tMin : (k >= bias + tMax ? tMax : k - bias);

  				if (digit < t) {
  					break;
  				}

  				baseMinusT = base - t;
  				if (w > floor(maxInt / baseMinusT)) {
  					error('overflow');
  				}

  				w *= baseMinusT;

  			}

  			out = output.length + 1;
  			bias = adapt(i - oldi, out, oldi == 0);

  			// `i` was supposed to wrap around from `out` to `0`,
  			// incrementing `n` each time, so we'll fix that now:
  			if (floor(i / out) > maxInt - n) {
  				error('overflow');
  			}

  			n += floor(i / out);
  			i %= out;

  			// Insert `n` at position `i` of the output
  			output.splice(i++, 0, n);

  		}

  		return ucs2encode(output);
  	}

  	/**
  	 * Converts a string of Unicode symbols (e.g. a domain name label) to a
  	 * Punycode string of ASCII-only symbols.
  	 * @memberOf punycode
  	 * @param {String} input The string of Unicode symbols.
  	 * @returns {String} The resulting Punycode string of ASCII-only symbols.
  	 */
  	function encode(input) {
  		var n,
  		    delta,
  		    handledCPCount,
  		    basicLength,
  		    bias,
  		    j,
  		    m,
  		    q,
  		    k,
  		    t,
  		    currentValue,
  		    output = [],
  		    /** `inputLength` will hold the number of code points in `input`. */
  		    inputLength,
  		    /** Cached calculation results */
  		    handledCPCountPlusOne,
  		    baseMinusT,
  		    qMinusT;

  		// Convert the input in UCS-2 to Unicode
  		input = ucs2decode(input);

  		// Cache the length
  		inputLength = input.length;

  		// Initialize the state
  		n = initialN;
  		delta = 0;
  		bias = initialBias;

  		// Handle the basic code points
  		for (j = 0; j < inputLength; ++j) {
  			currentValue = input[j];
  			if (currentValue < 0x80) {
  				output.push(stringFromCharCode(currentValue));
  			}
  		}

  		handledCPCount = basicLength = output.length;

  		// `handledCPCount` is the number of code points that have been handled;
  		// `basicLength` is the number of basic code points.

  		// Finish the basic string - if it is not empty - with a delimiter
  		if (basicLength) {
  			output.push(delimiter);
  		}

  		// Main encoding loop:
  		while (handledCPCount < inputLength) {

  			// All non-basic code points < n have been handled already. Find the next
  			// larger one:
  			for (m = maxInt, j = 0; j < inputLength; ++j) {
  				currentValue = input[j];
  				if (currentValue >= n && currentValue < m) {
  					m = currentValue;
  				}
  			}

  			// Increase `delta` enough to advance the decoder's <n,i> state to <m,0>,
  			// but guard against overflow
  			handledCPCountPlusOne = handledCPCount + 1;
  			if (m - n > floor((maxInt - delta) / handledCPCountPlusOne)) {
  				error('overflow');
  			}

  			delta += (m - n) * handledCPCountPlusOne;
  			n = m;

  			for (j = 0; j < inputLength; ++j) {
  				currentValue = input[j];

  				if (currentValue < n && ++delta > maxInt) {
  					error('overflow');
  				}

  				if (currentValue == n) {
  					// Represent delta as a generalized variable-length integer
  					for (q = delta, k = base; /* no condition */; k += base) {
  						t = k <= bias ? tMin : (k >= bias + tMax ? tMax : k - bias);
  						if (q < t) {
  							break;
  						}
  						qMinusT = q - t;
  						baseMinusT = base - t;
  						output.push(
  							stringFromCharCode(digitToBasic(t + qMinusT % baseMinusT, 0))
  						);
  						q = floor(qMinusT / baseMinusT);
  					}

  					output.push(stringFromCharCode(digitToBasic(q, 0)));
  					bias = adapt(delta, handledCPCountPlusOne, handledCPCount == basicLength);
  					delta = 0;
  					++handledCPCount;
  				}
  			}

  			++delta;
  			++n;

  		}
  		return output.join('');
  	}

  	/**
  	 * Converts a Punycode string representing a domain name or an email address
  	 * to Unicode. Only the Punycoded parts of the input will be converted, i.e.
  	 * it doesn't matter if you call it on a string that has already been
  	 * converted to Unicode.
  	 * @memberOf punycode
  	 * @param {String} input The Punycoded domain name or email address to
  	 * convert to Unicode.
  	 * @returns {String} The Unicode representation of the given Punycode
  	 * string.
  	 */
  	function toUnicode(input) {
  		return mapDomain(input, function(string) {
  			return regexPunycode.test(string)
  				? decode(string.slice(4).toLowerCase())
  				: string;
  		});
  	}

  	/**
  	 * Converts a Unicode string representing a domain name or an email address to
  	 * Punycode. Only the non-ASCII parts of the domain name will be converted,
  	 * i.e. it doesn't matter if you call it with a domain that's already in
  	 * ASCII.
  	 * @memberOf punycode
  	 * @param {String} input The domain name or email address to convert, as a
  	 * Unicode string.
  	 * @returns {String} The Punycode representation of the given domain name or
  	 * email address.
  	 */
  	function toASCII(input) {
  		return mapDomain(input, function(string) {
  			return regexNonASCII.test(string)
  				? 'xn--' + encode(string)
  				: string;
  		});
  	}

  	/*--------------------------------------------------------------------------*/

  	/** Define the public API */
  	punycode = {
  		/**
  		 * A string representing the current Punycode.js version number.
  		 * @memberOf punycode
  		 * @type String
  		 */
  		'version': '1.3.1',
  		/**
  		 * An object of methods to convert from JavaScript's internal character
  		 * representation (UCS-2) to Unicode code points, and back.
  		 * @see <https://mathiasbynens.be/notes/javascript-encoding>
  		 * @memberOf punycode
  		 * @type Object
  		 */
  		'ucs2': {
  			'decode': ucs2decode,
  			'encode': ucs2encode
  		},
  		'decode': decode,
  		'encode': encode,
  		'toASCII': toASCII,
  		'toUnicode': toUnicode
  	};

  	/** Expose `punycode` */
  	// Some AMD build optimizers, like r.js, check for specific condition patterns
  	// like the following:
  	if (
  		typeof define == 'function' &&
  		typeof define.amd == 'object' &&
  		define.amd
  	) {
  		define('punycode', function() {
  			return punycode;
  		});
  	} else if (freeExports && freeModule) {
  		if (module.exports == freeExports) { // in Node.js or RingoJS v0.8.0+
  			freeModule.exports = punycode;
  		} else { // in Narwhal or RingoJS v0.7.0-
  			for (key in punycode) {
  				punycode.hasOwnProperty(key) && (freeExports[key] = punycode[key]);
  			}
  		}
  	} else { // in Rhino or a web browser
  		root.punycode = punycode;
  	}

  }(this));

  var html2canvasNodeAttribute = "data-html2canvas-node";
  var html2canvasCanvasCloneAttribute = "data-html2canvas-canvas-clone";
  var html2canvasCanvasCloneIndex = 0;

  window.html2canvas = function(nodeList, options) {
      options = options || {};
      if (options.logging) {
          window.html2canvas.logging = true;
          window.html2canvas.start = Date.now();
      }

      options.async = typeof(options.async) === "undefined" ? true : options.async;
      options.allowTaint = typeof(options.allowTaint) === "undefined" ? false : options.allowTaint;
      options.removeContainer = typeof(options.removeContainer) === "undefined" ? true : options.removeContainer;
      options.javascriptEnabled = typeof(options.javascriptEnabled) === "undefined" ? false : options.javascriptEnabled;
      options.imageTimeout = typeof(options.imageTimeout) === "undefined" ? 10000 : options.imageTimeout;

      if (typeof(nodeList) === "string") {
          if (typeof(options.proxy) !== "string") {
              return Promise.reject("Proxy must be used when rendering url");
          }
          return loadUrlDocument(absoluteUrl(nodeList), options.proxy, document, window.innerWidth, window.innerHeight, options).then(function(container) {
              return renderWindow(container.contentWindow.document.documentElement, container, options, window.innerWidth, window.innerHeight);
          });
      }

      var node = ((nodeList === undefined) ? [document.documentElement] : ((nodeList.length) ? nodeList : [nodeList]))[0];
      node.setAttribute(html2canvasNodeAttribute, "true");
      return renderDocument(node.ownerDocument, options, node.ownerDocument.defaultView.innerWidth, node.ownerDocument.defaultView.innerHeight).then(function(canvas) {
          if (typeof(options.onrendered) === "function") {
              log("options.onrendered is deprecated, html2canvas returns a Promise containing the canvas");
              options.onrendered(canvas);
          }
          return canvas;
      });
  };

  window.html2canvas.punycode = this.punycode;
  window.html2canvas.proxy = {};

  function renderDocument(document, options, windowWidth, windowHeight) {
      return createWindowClone(document, document, windowWidth, windowHeight, options).then(function(container) {
          log("Document cloned");
          var selector = "[" + html2canvasNodeAttribute + "='true']";
          document.querySelector(selector).removeAttribute(html2canvasNodeAttribute);
          var clonedWindow = container.contentWindow;
          var node = clonedWindow.document.querySelector(selector);
          var oncloneHandler = (typeof(options.onclone) === "function") ? Promise.resolve(options.onclone(clonedWindow.document)) : Promise.resolve(true);
          return oncloneHandler.then(function() {
              return renderWindow(node, container, options, windowWidth, windowHeight);
          });
      });
  }

  function renderWindow(node, container, options, windowWidth, windowHeight) {
      var clonedWindow = container.contentWindow;
      var support = new Support(clonedWindow.document);
      var imageLoader = new ImageLoader(options, support);
      var bounds = getBounds(node);
      var width = options.type === "view" ? windowWidth : documentWidth(clonedWindow.document);
      var height = options.type === "view" ? windowHeight : documentHeight(clonedWindow.document);
      var renderer = new CanvasRenderer(width, height, imageLoader, options, document);
      var parser = new NodeParser(node, renderer, support, imageLoader, options);
      return parser.ready.then(function() {
          log("Finished rendering");
          var canvas;

          if (options.type === "view") {
              canvas = crop(renderer.canvas, {width: renderer.canvas.width, height: renderer.canvas.height, top: 0, left: 0, x: 0, y: 0});
          } else if (node === clonedWindow.document.body || node === clonedWindow.document.documentElement || options.canvas != null) {
              canvas = renderer.canvas;
          } else {
              canvas = crop(renderer.canvas, {width:  options.width != null ? options.width : bounds.width, height: options.height != null ? options.height : bounds.height, top: bounds.top, left: bounds.left, x: clonedWindow.pageXOffset, y: clonedWindow.pageYOffset});
          }

          cleanupContainer(container, options);
          return canvas;
      });
  }

  function cleanupContainer(container, options) {
      if (options.removeContainer) {
          container.parentNode.removeChild(container);
          log("Cleaned up container");
      }
  }

  function crop(canvas, bounds) {
      var croppedCanvas = document.createElement("canvas");
      var x1 = Math.min(canvas.width - 1, Math.max(0, bounds.left));
      var x2 = Math.min(canvas.width, Math.max(1, bounds.left + bounds.width));
      var y1 = Math.min(canvas.height - 1, Math.max(0, bounds.top));
      var y2 = Math.min(canvas.height, Math.max(1, bounds.top + bounds.height));
      croppedCanvas.width = bounds.width;
      croppedCanvas.height =  bounds.height;
      log("Cropping canvas at:", "left:", bounds.left, "top:", bounds.top, "width:", (x2-x1), "height:", (y2-y1));
      log("Resulting crop with width", bounds.width, "and height", bounds.height, " with x", x1, "and y", y1);
      croppedCanvas.getContext("2d").drawImage(canvas, x1, y1, x2-x1, y2-y1, bounds.x, bounds.y, x2-x1, y2-y1);
      return croppedCanvas;
  }

  function documentWidth (doc) {
      return Math.max(
          Math.max(doc.body.scrollWidth, doc.documentElement.scrollWidth),
          Math.max(doc.body.offsetWidth, doc.documentElement.offsetWidth),
          Math.max(doc.body.clientWidth, doc.documentElement.clientWidth)
      );
  }

  function documentHeight (doc) {
      return Math.max(
          Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight),
          Math.max(doc.body.offsetHeight, doc.documentElement.offsetHeight),
          Math.max(doc.body.clientHeight, doc.documentElement.clientHeight)
      );
  }

  function smallImage() {
      return "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
  }

  function createWindowClone(ownerDocument, containerDocument, width, height, options) {
      labelCanvasElements(ownerDocument);
      var documentElement = ownerDocument.documentElement.cloneNode(true),
          container = containerDocument.createElement("iframe");

      container.className = "html2canvas-container";
      container.style.visibility = "hidden";
      container.style.position = "fixed";
      container.style.left = "-10000px";
      container.style.top = "0px";
      container.style.border = "0";
      container.width = width;
      container.height = height;
      container.scrolling = "no"; // ios won't scroll without it
      containerDocument.body.appendChild(container);

      return new Promise(function(resolve) {
          var documentClone = container.contentWindow.document;
          /* Chrome doesn't detect relative background-images assigned in inline <style> sheets when fetched through getComputedStyle
          if window url is about:blank, we can assign the url to current by writing onto the document
           */
          container.contentWindow.onload = container.onload = function() {
              var interval = setInterval(function() {
                  if (documentClone.body.childNodes.length > 0) {
                      cloneCanvasContents(ownerDocument, documentClone);
                      clearInterval(interval);
                      if (options.type === "view") {
                          container.contentWindow.scrollTo(x, y);
                      }
                      resolve(container);
                  }
              }, 50);
          };

          var x = ownerDocument.defaultView.pageXOffset;
          var y = ownerDocument.defaultView.pageYOffset;

          documentClone.open();
          documentClone.write("<!DOCTYPE html><html></html>");
          // Chrome scrolls the parent document for some reason after the write to the cloned window???
          restoreOwnerScroll(ownerDocument, x, y);
          documentClone.replaceChild(options.javascriptEnabled === true ? documentClone.adoptNode(documentElement) : removeScriptNodes(documentClone.adoptNode(documentElement)), documentClone.documentElement);
          documentClone.close();
      });
  }

  function restoreOwnerScroll(ownerDocument, x, y) {
      if (x !== ownerDocument.defaultView.pageXOffset || y !== ownerDocument.defaultView.pageYOffset) {
          ownerDocument.defaultView.scrollTo(x, y);
      }
  }

  function loadUrlDocument(src, proxy, document, width, height, options) {
      return new Proxy(src, proxy, window.document).then(documentFromHTML(src)).then(function(doc) {
          return createWindowClone(doc, document, width, height, options);
      });
  }

  function documentFromHTML(src) {
      return function(html) {
          var parser = new DOMParser(), doc;
          try {
              doc = parser.parseFromString(html, "text/html");
          } catch(e) {
              log("DOMParser not supported, falling back to createHTMLDocument");
              doc = document.implementation.createHTMLDocument("");
              try {
                  doc.open();
                  doc.write(html);
                  doc.close();
              } catch(ee) {
                  log("createHTMLDocument write not supported, falling back to document.body.innerHTML");
                  doc.body.innerHTML = html; // ie9 doesnt support writing to documentElement
              }
          }

          var b = doc.querySelector("base");
          if (!b || !b.href.host) {
              var base = doc.createElement("base");
              base.href = src;
              doc.head.insertBefore(base, doc.head.firstChild);
          }

          return doc;
      };
  }


  function labelCanvasElements(ownerDocument) {
      [].slice.call(ownerDocument.querySelectorAll("canvas"), 0).forEach(function(canvas) {
          canvas.setAttribute(html2canvasCanvasCloneAttribute, "canvas-" + html2canvasCanvasCloneIndex++);
      });
  }

  function cloneCanvasContents(ownerDocument, documentClone) {
      [].slice.call(ownerDocument.querySelectorAll("[" + html2canvasCanvasCloneAttribute + "]"), 0).forEach(function(canvas) {
          try {
              var clonedCanvas = documentClone.querySelector('[' + html2canvasCanvasCloneAttribute + '="' + canvas.getAttribute(html2canvasCanvasCloneAttribute) + '"]');
              if (clonedCanvas) {
                  clonedCanvas.width = canvas.width;
                  clonedCanvas.height = canvas.height;
                  clonedCanvas.getContext("2d").putImageData(canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height), 0, 0);
              }
          } catch(e) {
              log("Unable to copy canvas content from", canvas, e);
          }
          canvas.removeAttribute(html2canvasCanvasCloneAttribute);
      });
  }

  function removeScriptNodes(parent) {
      [].slice.call(parent.childNodes, 0).filter(isElementNode).forEach(function(node) {
          if (node.tagName === "SCRIPT") {
              parent.removeChild(node);
          } else {
              removeScriptNodes(node);
          }
      });
      return parent;
  }

  function isElementNode(node) {
      return node.nodeType === Node.ELEMENT_NODE;
  }

  function absoluteUrl(url) {
      var link = document.createElement("a");
      link.href = url;
      link.href = link.href;
      return link;
  }

  function DummyImageContainer(src) {
      this.src = src;
      log("DummyImageContainer for", src);
      if (!this.promise || !this.image) {
          log("Initiating DummyImageContainer");
          DummyImageContainer.prototype.image = new Image();
          var image = this.image;
          DummyImageContainer.prototype.promise = new Promise(function(resolve, reject) {
              image.onload = resolve;
              image.onerror = reject;
              image.src = smallImage();
              if (image.complete === true) {
                  resolve(image);
              }
          });
      }
  }

  function Font(family, size) {
      var container = document.createElement('div'),
          img = document.createElement('img'),
          span = document.createElement('span'),
          sampleText = 'Hidden Text',
          baseline,
          middle;

      container.style.visibility = "hidden";
      container.style.fontFamily = family;
      container.style.fontSize = size;
      container.style.margin = 0;
      container.style.padding = 0;

      document.body.appendChild(container);

      img.src = smallImage();
      img.width = 1;
      img.height = 1;

      img.style.margin = 0;
      img.style.padding = 0;
      img.style.verticalAlign = "baseline";

      span.style.fontFamily = family;
      span.style.fontSize = size;
      span.style.margin = 0;
      span.style.padding = 0;

      span.appendChild(document.createTextNode(sampleText));
      container.appendChild(span);
      container.appendChild(img);
      baseline = (img.offsetTop - span.offsetTop) + 1;

      container.removeChild(span);
      container.appendChild(document.createTextNode(sampleText));

      container.style.lineHeight = "normal";
      img.style.verticalAlign = "super";

      middle = (img.offsetTop-container.offsetTop) + 1;

      document.body.removeChild(container);

      this.baseline = baseline;
      this.lineWidth = 1;
      this.middle = middle;
  }

  function FontMetrics() {
      this.data = {};
  }

  FontMetrics.prototype.getMetrics = function(family, size) {
      if (this.data[family + "-" + size] === undefined) {
          this.data[family + "-" + size] = new Font(family, size);
      }
      return this.data[family + "-" + size];
  };

  function FrameContainer(container, sameOrigin, options) {
      this.image = null;
      this.src = container;
      var self = this;
      var bounds = getBounds(container);
      this.promise = (!sameOrigin ? this.proxyLoad(options.proxy, bounds, options) : new Promise(function(resolve) {
          if (container.contentWindow.document.URL === "about:blank" || container.contentWindow.document.documentElement == null) {
              container.contentWindow.onload = container.onload = function() {
                  resolve(container);
              };
          } else {
              resolve(container);
          }
      })).then(function(container) {
          return html2canvas(container.contentWindow.document.documentElement, {type: 'view', width: container.width, height: container.height, proxy: options.proxy, javascriptEnabled: options.javascriptEnabled, removeContainer: options.removeContainer, allowTaint: options.allowTaint, imageTimeout: options.imageTimeout / 2});
      }).then(function(canvas) {
          return self.image = canvas;
      });
  }

  FrameContainer.prototype.proxyLoad = function(proxy, bounds, options) {
      var container = this.src;
      return loadUrlDocument(container.src, proxy, container.ownerDocument, bounds.width, bounds.height, options);
  };

  function GradientContainer(imageData) {
      this.src = imageData.value;
      this.colorStops = [];
      this.type = null;
      this.x0 = 0.5;
      this.y0 = 0.5;
      this.x1 = 0.5;
      this.y1 = 0.5;
      this.promise = Promise.resolve(true);
  }

  GradientContainer.prototype.TYPES = {
      LINEAR: 1,
      RADIAL: 2
  };

  function ImageContainer(src, cors) {
      this.src = src;
      this.image = new Image();
      var self = this;
      this.tainted = null;
      this.promise = new Promise(function(resolve, reject) {
          self.image.onload = resolve;
          self.image.onerror = reject;
          if (cors) {
              self.image.crossOrigin = "anonymous";
          }
          self.image.src = src;
          if (self.image.complete === true) {
              resolve(self.image);
          }
      });
  }

  function ImageLoader(options, support) {
      this.link = null;
      this.options = options;
      this.support = support;
      this.origin = this.getOrigin(window.location.href);
  }

  ImageLoader.prototype.findImages = function(nodes) {
      var images = [];
      nodes.reduce(function(imageNodes, container) {
          switch(container.node.nodeName) {
          case "IMG":
              return imageNodes.concat([{
                  args: [container.node.src],
                  method: "url"
              }]);
          case "svg":
          case "IFRAME":
              return imageNodes.concat([{
                  args: [container.node],
                  method: container.node.nodeName
              }]);
          }
          return imageNodes;
      }, []).forEach(this.addImage(images, this.loadImage), this);
      return images;
  };

  ImageLoader.prototype.findBackgroundImage = function(images, container) {
      container.parseBackgroundImages().filter(this.hasImageBackground).forEach(this.addImage(images, this.loadImage), this);
      return images;
  };

  ImageLoader.prototype.addImage = function(images, callback) {
      return function(newImage) {
          newImage.args.forEach(function(image) {
              if (!this.imageExists(images, image)) {
                  images.splice(0, 0, callback.call(this, newImage));
                  log('Added image #' + (images.length), typeof(image) === "string" ? image.substring(0, 100) : image);
              }
          }, this);
      };
  };

  ImageLoader.prototype.hasImageBackground = function(imageData) {
      return imageData.method !== "none";
  };

  ImageLoader.prototype.loadImage = function(imageData) {
      if (imageData.method === "url") {
          var src = imageData.args[0];
          if (this.isSVG(src) && !this.support.svg && !this.options.allowTaint) {
              return new SVGContainer(src);
          } else if (src.match(/data:image\/.*;base64,/i)) {
              return new ImageContainer(src.replace(/url\(['"]{0,}|['"]{0,}\)$/ig, ''), false);
          } else if (this.isSameOrigin(src) || this.options.allowTaint === true || this.isSVG(src)) {
              return new ImageContainer(src, false);
          } else if (this.support.cors && !this.options.allowTaint && this.options.useCORS) {
              return new ImageContainer(src, true);
          } else if (this.options.proxy) {
              return new ProxyImageContainer(src, this.options.proxy);
          } else {
              return new DummyImageContainer(src);
          }
      } else if (imageData.method === "linear-gradient") {
          return new LinearGradientContainer(imageData);
      } else if (imageData.method === "gradient") {
          return new WebkitGradientContainer(imageData);
      } else if (imageData.method === "svg") {
          return new SVGNodeContainer(imageData.args[0], this.support.svg);
      } else if (imageData.method === "IFRAME") {
          return new FrameContainer(imageData.args[0], this.isSameOrigin(imageData.args[0].src), this.options);
      } else {
          return new DummyImageContainer(imageData);
      }
  };

  ImageLoader.prototype.isSVG = function(src) {
      return src.substring(src.length - 3).toLowerCase() === "svg" || SVGContainer.prototype.isInline(src);
  };

  ImageLoader.prototype.imageExists = function(images, src) {
      return images.some(function(image) {
          return image.src === src;
      });
  };

  ImageLoader.prototype.isSameOrigin = function(url) {
      return (this.getOrigin(url) === this.origin);
  };

  ImageLoader.prototype.getOrigin = function(url) {
      var link = this.link || (this.link = document.createElement("a"));
      link.href = url;
      link.href = link.href; // IE9, LOL! - http://jsfiddle.net/niklasvh/2e48b/
      return link.protocol + link.hostname + link.port;
  };

  ImageLoader.prototype.getPromise = function(container) {
      return this.timeout(container, this.options.imageTimeout)['catch'](function() {
          var dummy = new DummyImageContainer(container.src);
          return dummy.promise.then(function(image) {
              container.image = image;
          });
      });
  };

  ImageLoader.prototype.get = function(src) {
      var found = null;
      return this.images.some(function(img) {
          return (found = img).src === src;
      }) ? found : null;
  };

  ImageLoader.prototype.fetch = function(nodes) {
      this.images = nodes.reduce(bind(this.findBackgroundImage, this), this.findImages(nodes));
      this.images.forEach(function(image, index) {
          image.promise.then(function() {
              log("Succesfully loaded image #"+ (index+1), image);
          }, function(e) {
              log("Failed loading image #"+ (index+1), image, e);
          });
      });
      this.ready = Promise.all(this.images.map(this.getPromise, this));
      log("Finished searching images");
      return this;
  };

  ImageLoader.prototype.timeout = function(container, timeout) {
      var timer;
      return Promise.race([container.promise, new Promise(function(res, reject) {
          timer = setTimeout(function() {
              log("Timed out loading image", container);
              reject(container);
          }, timeout);
      })]).then(function(container) {
          clearTimeout(timer);
          return container;
      });
  };

  function LinearGradientContainer(imageData) {
      GradientContainer.apply(this, arguments);
      this.type = this.TYPES.LINEAR;

      var hasDirection = imageData.args[0].match(this.stepRegExp) === null;

      if (hasDirection) {
          imageData.args[0].split(" ").reverse().forEach(function(position) {
              switch(position) {
                  case "left":
                      this.x0 = 0;
                      this.x1 = 1;
                      break;
                  case "top":
                      this.y0 = 0;
                      this.y1 = 1;
                      break;
                  case "right":
                      this.x0 = 1;
                      this.x1 = 0;
                      break;
                  case "bottom":
                      this.y0 = 1;
                      this.y1 = 0;
                      break;
                  case "to":
                      var y0 = this.y0;
                      var x0 = this.x0;
                      this.y0 = this.y1;
                      this.x0 = this.x1;
                      this.x1 = x0;
                      this.y1 = y0;
                      break;
              }
          }, this);
      } else {
          this.y0 = 0;
          this.y1 = 1;
      }

      this.colorStops = imageData.args.slice(hasDirection ? 1 : 0).map(function(colorStop) {
          var colorStopMatch = colorStop.match(this.stepRegExp);
          return {
              color: colorStopMatch[1],
              stop: colorStopMatch[3] === "%" ? colorStopMatch[2] / 100 : null
          };
      }, this);

      if (this.colorStops[0].stop === null) {
          this.colorStops[0].stop = 0;
      }

      if (this.colorStops[this.colorStops.length - 1].stop === null) {
          this.colorStops[this.colorStops.length - 1].stop = 1;
      }

      this.colorStops.forEach(function(colorStop, index) {
          if (colorStop.stop === null) {
              this.colorStops.slice(index).some(function(find, count) {
                  if (find.stop !== null) {
                      colorStop.stop = ((find.stop - this.colorStops[index - 1].stop) / (count + 1)) + this.colorStops[index - 1].stop;
                      return true;
                  } else {
                      return false;
                  }
              }, this);
          }
      }, this);
  }

  LinearGradientContainer.prototype = Object.create(GradientContainer.prototype);

  LinearGradientContainer.prototype.stepRegExp = /((?:rgb|rgba)\(\d{1,3},\s\d{1,3},\s\d{1,3}(?:,\s[0-9\.]+)?\))\s*(\d{1,3})?(%|px)?/;

  function log() {
      if (window.html2canvas.logging && window.console && window.console.log) {
          Function.prototype.bind.call(window.console.log, (window.console)).apply(window.console, [(Date.now() - window.html2canvas.start) + "ms", "html2canvas:"].concat([].slice.call(arguments, 0)));
      }
  }

  function NodeContainer(node, parent) {
      this.node = node;
      this.parent = parent;
      this.stack = null;
      this.bounds = null;
      this.borders = null;
      this.clip = [];
      this.backgroundClip = [];
      this.offsetBounds = null;
      this.visible = null;
      this.computedStyles = null;
      this.styles = {};
      this.backgroundImages = null;
      this.transformData = null;
      this.transformMatrix = null;
      this.isPseudoElement = false;
      this.opacity = null;
  }

  NodeContainer.prototype.cloneTo = function(stack) {
      stack.visible = this.visible;
      stack.borders = this.borders;
      stack.bounds = this.bounds;
      stack.clip = this.clip;
      stack.backgroundClip = this.backgroundClip;
      stack.computedStyles = this.computedStyles;
      stack.styles = this.styles;
      stack.backgroundImages = this.backgroundImages;
      stack.opacity = this.opacity;
  };

  NodeContainer.prototype.getOpacity = function() {
      return this.opacity === null ? (this.opacity = this.cssFloat('opacity')) : this.opacity;
  };

  NodeContainer.prototype.assignStack = function(stack) {
      this.stack = stack;
      stack.children.push(this);
  };

  NodeContainer.prototype.isElementVisible = function() {
      return this.node.nodeType === Node.TEXT_NODE ? this.parent.visible : (
          this.css('display') !== "none" &&
          this.css('visibility') !== "hidden" &&
          !this.node.hasAttribute("data-html2canvas-ignore") &&
          (this.node.nodeName !== "INPUT" || this.node.getAttribute("type") !== "hidden")
      );
  };

  NodeContainer.prototype.css = function(attribute) {
      if (!this.computedStyles) {
          this.computedStyles = this.isPseudoElement ? this.parent.computedStyle(this.before ? ":before" : ":after") : this.computedStyle(null);
      }

      return this.styles[attribute] || (this.styles[attribute] = this.computedStyles[attribute]);
  };

  NodeContainer.prototype.prefixedCss = function(attribute) {
      var prefixes = ["webkit", "moz", "ms", "o"];
      var value = this.css(attribute);
      if (value === undefined) {
          prefixes.some(function(prefix) {
              value = this.css(prefix + attribute.substr(0, 1).toUpperCase() + attribute.substr(1));
              return value !== undefined;
          }, this);
      }
      return value === undefined ? null : value;
  };

  NodeContainer.prototype.computedStyle = function(type) {
      return this.node.ownerDocument.defaultView.getComputedStyle(this.node, type);
  };

  NodeContainer.prototype.cssInt = function(attribute) {
      var value = parseInt(this.css(attribute), 10);
      return (isNaN(value)) ? 0 : value; // borders in old IE are throwing 'medium' for demo.html
  };

  NodeContainer.prototype.cssFloat = function(attribute) {
      var value = parseFloat(this.css(attribute));
      return (isNaN(value)) ? 0 : value;
  };

  NodeContainer.prototype.fontWeight = function() {
      var weight = this.css("fontWeight");
      switch(parseInt(weight, 10)){
      case 401:
          weight = "bold";
          break;
      case 400:
          weight = "normal";
          break;
      }
      return weight;
  };

  NodeContainer.prototype.parseClip = function() {
      var matches = this.css('clip').match(this.CLIP);
      if (matches) {
          return {
              top: parseInt(matches[1], 10),
              right: parseInt(matches[2], 10),
              bottom: parseInt(matches[3], 10),
              left: parseInt(matches[4], 10)
          };
      }
      return null;
  };

  NodeContainer.prototype.parseBackgroundImages = function() {
      return this.backgroundImages || (this.backgroundImages = parseBackgrounds(this.css("backgroundImage")));
  };

  NodeContainer.prototype.cssList = function(property, index) {
      var value = (this.css(property) || '').split(',');
      value = value[index || 0] || value[0] || 'auto';
      value = value.trim().split(' ');
      if (value.length === 1) {
          value = [value[0], value[0]];
      }
      return value;
  };

  NodeContainer.prototype.parseBackgroundSize = function(bounds, image, index) {
      var size = this.cssList("backgroundSize", index);
      var width, height;

      if (isPercentage(size[0])) {
          width = bounds.width * parseFloat(size[0]) / 100;
      } else if (/contain|cover/.test(size[0])) {
          var targetRatio = bounds.width / bounds.height, currentRatio = image.width / image.height;
          return (targetRatio < currentRatio ^ size[0] === 'contain') ?  {width: bounds.height * currentRatio, height: bounds.height} : {width: bounds.width, height: bounds.width / currentRatio};
      } else {
          width = parseInt(size[0], 10);
      }

      if (size[0] === 'auto' && size[1] === 'auto') {
          height = image.height;
      } else if (size[1] === 'auto') {
          height = width / image.width * image.height;
      } else if (isPercentage(size[1])) {
          height =  bounds.height * parseFloat(size[1]) / 100;
      } else {
          height = parseInt(size[1], 10);
      }

      if (size[0] === 'auto') {
          width = height / image.height * image.width;
      }

      return {width: width, height: height};
  };

  NodeContainer.prototype.parseBackgroundPosition = function(bounds, image, index, backgroundSize) {
      var position = this.cssList('backgroundPosition', index);
      var left, top;

      if (isPercentage(position[0])){
          left = (bounds.width - (backgroundSize || image).width) * (parseFloat(position[0]) / 100);
      } else {
          left = parseInt(position[0], 10);
      }

      if (position[1] === 'auto') {
          top = left / image.width * image.height;
      } else if (isPercentage(position[1])){
          top =  (bounds.height - (backgroundSize || image).height) * parseFloat(position[1]) / 100;
      } else {
          top = parseInt(position[1], 10);
      }

      if (position[0] === 'auto') {
          left = top / image.height * image.width;
      }

      return {left: left, top: top};
  };

  NodeContainer.prototype.parseBackgroundRepeat = function(index) {
      return this.cssList("backgroundRepeat", index)[0];
  };

  NodeContainer.prototype.parseTextShadows = function() {
      var textShadow = this.css("textShadow");
      var results = [];

      if (textShadow && textShadow !== 'none') {
          var shadows = textShadow.match(this.TEXT_SHADOW_PROPERTY);
          for (var i = 0; shadows && (i < shadows.length); i++) {
              var s = shadows[i].match(this.TEXT_SHADOW_VALUES);
              results.push({
                  color: s[0],
                  offsetX: s[1] ? parseFloat(s[1].replace('px', '')) : 0,
                  offsetY: s[2] ? parseFloat(s[2].replace('px', '')) : 0,
                  blur: s[3] ? s[3].replace('px', '') : 0
              });
          }
      }
      return results;
  };

  NodeContainer.prototype.parseTransform = function() {
      if (!this.transformData) {
          if (this.hasTransform()) {
              var offset = this.parseBounds();
              var origin = this.prefixedCss("transformOrigin").split(" ").map(removePx).map(asFloat);
              origin[0] += offset.left;
              origin[1] += offset.top;
              this.transformData = {
                  origin: origin,
                  matrix: this.parseTransformMatrix()
              };
          } else {
              this.transformData = {
                  origin: [0, 0],
                  matrix: [1, 0, 0, 1, 0, 0]
              };
          }
      }
      return this.transformData;
  };

  NodeContainer.prototype.parseTransformMatrix = function() {
      if (!this.transformMatrix) {
          var transform = this.prefixedCss("transform");
          var matrix = transform ? parseMatrix(transform.match(this.MATRIX_PROPERTY)) : null;
          this.transformMatrix = matrix ? matrix : [1, 0, 0, 1, 0, 0];
      }
      return this.transformMatrix;
  };

  NodeContainer.prototype.parseBounds = function() {
      return this.bounds || (this.bounds = this.hasTransform() ? offsetBounds(this.node) : getBounds(this.node));
  };

  NodeContainer.prototype.hasTransform = function() {
      return this.parseTransformMatrix().join(",") !== "1,0,0,1,0,0" || (this.parent && this.parent.hasTransform());
  };

  NodeContainer.prototype.getValue = function() {
      var value = this.node.value || "";
      value = (this.node.tagName === "SELECT") ? selectionValue(this.node) : value;
      return value.length === 0 ? (this.node.placeholder || "") : value;
  };

  NodeContainer.prototype.MATRIX_PROPERTY = /(matrix)\((.+)\)/;
  NodeContainer.prototype.TEXT_SHADOW_PROPERTY = /((rgba|rgb)\([^\)]+\)(\s-?\d+px){0,})/g;
  NodeContainer.prototype.TEXT_SHADOW_VALUES = /(-?\d+px)|(#.+)|(rgb\(.+\))|(rgba\(.+\))/g;
  NodeContainer.prototype.CLIP = /^rect\((\d+)px,? (\d+)px,? (\d+)px,? (\d+)px\)$/;

  function selectionValue(node) {
      var option = node.options[node.selectedIndex || 0];
      return option ? (option.text || "") : "";
  }

  function parseMatrix(match) {
      if (match && match[1] === "matrix") {
          return match[2].split(",").map(function(s) {
              return parseFloat(s.trim());
          });
      }
  }

  function isPercentage(value) {
      return value.toString().indexOf("%") !== -1;
  }

  function parseBackgrounds(backgroundImage) {
      var whitespace = ' \r\n\t',
          method, definition, prefix, prefix_i, block, results = [],
          mode = 0, numParen = 0, quote, args;
      var appendResult = function() {
          if(method) {
              if (definition.substr(0, 1) === '"') {
                  definition = definition.substr(1, definition.length - 2);
              }
              if (definition) {
                  args.push(definition);
              }
              if (method.substr(0, 1) === '-' && (prefix_i = method.indexOf('-', 1 ) + 1) > 0) {
                  prefix = method.substr(0, prefix_i);
                  method = method.substr(prefix_i);
              }
              results.push({
                  prefix: prefix,
                  method: method.toLowerCase(),
                  value: block,
                  args: args,
                  image: null
              });
          }
          args = [];
          method = prefix = definition = block = '';
      };
      args = [];
      method = prefix = definition = block = '';
      backgroundImage.split("").forEach(function(c) {
          if (mode === 0 && whitespace.indexOf(c) > -1) {
              return;
          }
          switch(c) {
          case '"':
              if(!quote) {
                  quote = c;
              } else if(quote === c) {
                  quote = null;
              }
              break;
          case '(':
              if(quote) {
                  break;
              } else if(mode === 0) {
                  mode = 1;
                  block += c;
                  return;
              } else {
                  numParen++;
              }
              break;
          case ')':
              if (quote) {
                  break;
              } else if(mode === 1) {
                  if(numParen === 0) {
                      mode = 0;
                      block += c;
                      appendResult();
                      return;
                  } else {
                      numParen--;
                  }
              }
              break;

          case ',':
              if (quote) {
                  break;
              } else if(mode === 0) {
                  appendResult();
                  return;
              } else if (mode === 1) {
                  if (numParen === 0 && !method.match(/^url$/i)) {
                      args.push(definition);
                      definition = '';
                      block += c;
                      return;
                  }
              }
              break;
          }

          block += c;
          if (mode === 0) {
              method += c;
          } else {
              definition += c;
          }
      });

      appendResult();
      return results;
  }

  function removePx(str) {
      return str.replace("px", "");
  }

  function asFloat(str) {
      return parseFloat(str);
  }

  function getBounds(node) {
      if (node.getBoundingClientRect) {
          var clientRect = node.getBoundingClientRect();
          var width = node.offsetWidth == null ? clientRect.width : node.offsetWidth;
          return {
              top: clientRect.top,
              bottom: clientRect.bottom || (clientRect.top + clientRect.height),
              right: clientRect.left + width,
              left: clientRect.left,
              width:  width,
              height: node.offsetHeight == null ? clientRect.height : node.offsetHeight
          };
      }
      return {};
  }

  function offsetBounds(node) {
      var parent = node.offsetParent ? offsetBounds(node.offsetParent) : {top: 0, left: 0};

      return {
          top: node.offsetTop + parent.top,
          bottom: node.offsetTop + node.offsetHeight + parent.top,
          right: node.offsetLeft + parent.left + node.offsetWidth,
          left: node.offsetLeft + parent.left,
          width: node.offsetWidth,
          height: node.offsetHeight
      };
  }

  function NodeParser(element, renderer, support, imageLoader, options) {
      log("Starting NodeParser");
      this.renderer = renderer;
      this.options = options;
      this.range = null;
      this.support = support;
      this.renderQueue = [];
      this.stack = new StackingContext(true, 1, element.ownerDocument, null);
      var parent = new NodeContainer(element, null);
      if (element === element.ownerDocument.documentElement) {
          // http://www.w3.org/TR/css3-background/#special-backgrounds
          var canvasBackground = new NodeContainer(this.renderer.isTransparent(parent.css('backgroundColor')) ? element.ownerDocument.body : element.ownerDocument.documentElement, null);
          renderer.rectangle(0, 0, renderer.width, renderer.height, canvasBackground.css('backgroundColor'));
      }
      parent.visibile = parent.isElementVisible();
      this.createPseudoHideStyles(element.ownerDocument);
      this.disableAnimations(element.ownerDocument);
      this.nodes = flatten([parent].concat(this.getChildren(parent)).filter(function(container) {
          return container.visible = container.isElementVisible();
      }).map(this.getPseudoElements, this));
      this.fontMetrics = new FontMetrics();
      log("Fetched nodes, total:", this.nodes.length);
      log("Calculate overflow clips");
      this.calculateOverflowClips();
      log("Start fetching images");
      this.images = imageLoader.fetch(this.nodes.filter(isElement));
      this.ready = this.images.ready.then(bind(function() {
          log("Images loaded, starting parsing");
          log("Creating stacking contexts");
          this.createStackingContexts();
          log("Sorting stacking contexts");
          this.sortStackingContexts(this.stack);
          this.parse(this.stack);
          log("Render queue created with " + this.renderQueue.length + " items");
          return new Promise(bind(function(resolve) {
              if (!options.async) {
                  this.renderQueue.forEach(this.paint, this);
                  resolve();
              } else if (typeof(options.async) === "function") {
                  options.async.call(this, this.renderQueue, resolve);
              } else if (this.renderQueue.length > 0){
                  this.renderIndex = 0;
                  this.asyncRenderer(this.renderQueue, resolve);
              } else {
                  resolve();
              }
          }, this));
      }, this));
  }

  NodeParser.prototype.calculateOverflowClips = function() {
      this.nodes.forEach(function(container) {
          if (isElement(container)) {
              if (isPseudoElement(container)) {
                  container.appendToDOM();
              }
              container.borders = this.parseBorders(container);
              var clip = (container.css('overflow') === "hidden") ? [container.borders.clip] : [];
              var cssClip = container.parseClip();
              if (cssClip && ["absolute", "fixed"].indexOf(container.css('position')) !== -1) {
                  clip.push([["rect",
                          container.bounds.left + cssClip.left,
                          container.bounds.top + cssClip.top,
                          cssClip.right - cssClip.left,
                          cssClip.bottom - cssClip.top
                  ]]);
              }
              container.clip = hasParentClip(container) ? container.parent.clip.concat(clip) : clip;
              container.backgroundClip = (container.css('overflow') !== "hidden") ? container.clip.concat([container.borders.clip]) : container.clip;
              if (isPseudoElement(container)) {
                  container.cleanDOM();
              }
          } else if (isTextNode(container)) {
              container.clip = hasParentClip(container) ? container.parent.clip : [];
          }
          if (!isPseudoElement(container)) {
              container.bounds = null;
          }
      }, this);
  };

  function hasParentClip(container) {
      return container.parent && container.parent.clip.length;
  }

  NodeParser.prototype.asyncRenderer = function(queue, resolve, asyncTimer) {
      asyncTimer = asyncTimer || Date.now();
      this.paint(queue[this.renderIndex++]);
      if (queue.length === this.renderIndex) {
          resolve();
      } else if (asyncTimer + 20 > Date.now()) {
          this.asyncRenderer(queue, resolve, asyncTimer);
      } else {
          setTimeout(bind(function() {
              this.asyncRenderer(queue, resolve);
          }, this), 0);
      }
  };

  NodeParser.prototype.createPseudoHideStyles = function(document) {
      this.createStyles(document, '.' + PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_BEFORE + ':before { content: "" !important; display: none !important; }' +
          '.' + PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_AFTER + ':after { content: "" !important; display: none !important; }');
  };

  NodeParser.prototype.disableAnimations = function(document) {
      this.createStyles(document, '* { -webkit-animation: none !important; -moz-animation: none !important; -o-animation: none !important; animation: none !important; ' +
          '-webkit-transition: none !important; -moz-transition: none !important; -o-transition: none !important; transition: none !important;}');
  };

  NodeParser.prototype.createStyles = function(document, styles) {
      var hidePseudoElements = document.createElement('style');
      hidePseudoElements.innerHTML = styles;
      document.body.appendChild(hidePseudoElements);
  };

  NodeParser.prototype.getPseudoElements = function(container) {
      var nodes = [[container]];
      if (container.node.nodeType === Node.ELEMENT_NODE) {
          var before = this.getPseudoElement(container, ":before");
          var after = this.getPseudoElement(container, ":after");

          if (before) {
              nodes.push(before);
          }

          if (after) {
              nodes.push(after);
          }
      }
      return flatten(nodes);
  };

  function toCamelCase(str) {
      return str.replace(/(\-[a-z])/g, function(match){
          return match.toUpperCase().replace('-','');
      });
  }

  NodeParser.prototype.getPseudoElement = function(container, type) {
      var style = container.computedStyle(type);
      if(!style || !style.content || style.content === "none" || style.content === "-moz-alt-content" || style.display === "none") {
          return null;
      }

      var content = stripQuotes(style.content);
      var isImage = content.substr(0, 3) === 'url';
      var pseudoNode = document.createElement(isImage ? 'img' : 'html2canvaspseudoelement');
      var pseudoContainer = new PseudoElementContainer(pseudoNode, container, type);

      for (var i = style.length-1; i >= 0; i--) {
          var property = toCamelCase(style.item(i));
          pseudoNode.style[property] = style[property];
      }

      pseudoNode.className = PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_BEFORE + " " + PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_AFTER;

      if (isImage) {
          pseudoNode.src = parseBackgrounds(content)[0].args[0];
          return [pseudoContainer];
      } else {
          var text = document.createTextNode(content);
          pseudoNode.appendChild(text);
          return [pseudoContainer, new TextContainer(text, pseudoContainer)];
      }
  };


  NodeParser.prototype.getChildren = function(parentContainer) {
      return flatten([].filter.call(parentContainer.node.childNodes, renderableNode).map(function(node) {
          var container = [node.nodeType === Node.TEXT_NODE ? new TextContainer(node, parentContainer) : new NodeContainer(node, parentContainer)].filter(nonIgnoredElement);
          return node.nodeType === Node.ELEMENT_NODE && container.length && node.tagName !== "TEXTAREA" ? (container[0].isElementVisible() ? container.concat(this.getChildren(container[0])) : []) : container;
      }, this));
  };

  NodeParser.prototype.newStackingContext = function(container, hasOwnStacking) {
      var stack = new StackingContext(hasOwnStacking, container.getOpacity(), container.node, container.parent);
      container.cloneTo(stack);
      var parentStack = hasOwnStacking ? stack.getParentStack(this) : stack.parent.stack;
      parentStack.contexts.push(stack);
      container.stack = stack;
  };

  NodeParser.prototype.createStackingContexts = function() {
      this.nodes.forEach(function(container) {
          if (isElement(container) && (this.isRootElement(container) || hasOpacity(container) || isPositionedForStacking(container) || this.isBodyWithTransparentRoot(container) || container.hasTransform())) {
              this.newStackingContext(container, true);
          } else if (isElement(container) && ((isPositioned(container) && zIndex0(container)) || isInlineBlock(container) || isFloating(container))) {
              this.newStackingContext(container, false);
          } else {
              container.assignStack(container.parent.stack);
          }
      }, this);
  };

  NodeParser.prototype.isBodyWithTransparentRoot = function(container) {
      return container.node.nodeName === "BODY" && this.renderer.isTransparent(container.parent.css('backgroundColor'));
  };

  NodeParser.prototype.isRootElement = function(container) {
      return container.parent === null;
  };

  NodeParser.prototype.sortStackingContexts = function(stack) {
      stack.contexts.sort(zIndexSort(stack.contexts.slice(0)));
      stack.contexts.forEach(this.sortStackingContexts, this);
  };

  NodeParser.prototype.parseTextBounds = function(container) {
      return function(text, index, textList) {
          if (container.parent.css("textDecoration").substr(0, 4) !== "none" || text.trim().length !== 0) {
              if (this.support.rangeBounds && !container.parent.hasTransform()) {
                  var offset = textList.slice(0, index).join("").length;
                  return this.getRangeBounds(container.node, offset, text.length);
              } else if (container.node && typeof(container.node.data) === "string") {
                  var replacementNode = container.node.splitText(text.length);
                  var bounds = this.getWrapperBounds(container.node, container.parent.hasTransform());
                  container.node = replacementNode;
                  return bounds;
              }
          } else if(!this.support.rangeBounds || container.parent.hasTransform()){
              container.node = container.node.splitText(text.length);
          }
          return {};
      };
  };

  NodeParser.prototype.getWrapperBounds = function(node, transform) {
      var wrapper = node.ownerDocument.createElement('html2canvaswrapper');
      var parent = node.parentNode,
          backupText = node.cloneNode(true);

      wrapper.appendChild(node.cloneNode(true));
      parent.replaceChild(wrapper, node);
      var bounds = transform ? offsetBounds(wrapper) : getBounds(wrapper);
      parent.replaceChild(backupText, wrapper);
      return bounds;
  };

  NodeParser.prototype.getRangeBounds = function(node, offset, length) {
      var range = this.range || (this.range = node.ownerDocument.createRange());
      range.setStart(node, offset);
      range.setEnd(node, offset + length);
      return range.getBoundingClientRect();
  };

  function ClearTransform() {}

  NodeParser.prototype.parse = function(stack) {
      // http://www.w3.org/TR/CSS21/visuren.html#z-index
      var negativeZindex = stack.contexts.filter(negativeZIndex); // 2. the child stacking contexts with negative stack levels (most negative first).
      var descendantElements = stack.children.filter(isElement);
      var descendantNonFloats = descendantElements.filter(not(isFloating));
      var nonInlineNonPositionedDescendants = descendantNonFloats.filter(not(isPositioned)).filter(not(inlineLevel)); // 3 the in-flow, non-inline-level, non-positioned descendants.
      var nonPositionedFloats = descendantElements.filter(not(isPositioned)).filter(isFloating); // 4. the non-positioned floats.
      var inFlow = descendantNonFloats.filter(not(isPositioned)).filter(inlineLevel); // 5. the in-flow, inline-level, non-positioned descendants, including inline tables and inline blocks.
      var stackLevel0 = stack.contexts.concat(descendantNonFloats.filter(isPositioned)).filter(zIndex0); // 6. the child stacking contexts with stack level 0 and the positioned descendants with stack level 0.
      var text = stack.children.filter(isTextNode).filter(hasText);
      var positiveZindex = stack.contexts.filter(positiveZIndex); // 7. the child stacking contexts with positive stack levels (least positive first).
      negativeZindex.concat(nonInlineNonPositionedDescendants).concat(nonPositionedFloats)
          .concat(inFlow).concat(stackLevel0).concat(text).concat(positiveZindex).forEach(function(container) {
              this.renderQueue.push(container);
              if (isStackingContext(container)) {
                  this.parse(container);
                  this.renderQueue.push(new ClearTransform());
              }
          }, this);
  };

  NodeParser.prototype.paint = function(container) {
      try {
          if (container instanceof ClearTransform) {
              this.renderer.ctx.restore();
          } else if (isTextNode(container)) {
              if (isPseudoElement(container.parent)) {
                  container.parent.appendToDOM();
              }
              this.paintText(container);
              if (isPseudoElement(container.parent)) {
                  container.parent.cleanDOM();
              }
          } else {
              this.paintNode(container);
          }
      } catch(e) {
          log(e);
      }
  };

  NodeParser.prototype.paintNode = function(container) {
      if (isStackingContext(container)) {
          this.renderer.setOpacity(container.opacity);
          this.renderer.ctx.save();
          if (container.hasTransform()) {
              this.renderer.setTransform(container.parseTransform());
          }
      }
      if (container.node.nodeName === "INPUT" && container.node.type === "checkbox") {
          this.paintCheckbox(container);
      } else if (container.node.nodeName === "INPUT" && container.node.type === "radio") {
          this.paintRadio(container);
      } else {
          if (container.css('page-break-before') === 'always'){
          	var c = this.options.canvas.getContext('2d');
          	if (typeof c._pageBreakAt === 'function' ){
          		c._pageBreakAt(container.node.offsetTop);
          	}
          }
          this.paintElement(container);
      }
      if (container.node.getAttribute){
  		   var name = container.node.getAttribute('name');
  		   if (name === null){
  			   var name = container.node.getAttribute('id')			   
  		   }
  		   if (name !== null){
  			   var annotations = this.options.canvas.annotations;
  			   if (annotations){
  				   annotations.setName(name, container.bounds);
  			   }
  		   }
  	}
  };

  NodeParser.prototype.paintElement = function(container) {
      var bounds = container.parseBounds();
      this.renderer.clip(container.backgroundClip, function() {
          this.renderer.renderBackground(container, bounds, container.borders.borders.map(getWidth));
      }, this);

      this.renderer.clip(container.clip, function() {
          this.renderer.renderBorders(container.borders.borders);
      }, this);

      this.renderer.clip(container.backgroundClip, function() {
          switch (container.node.nodeName) {
          case "svg":
          case "IFRAME":
              var imgContainer = this.images.get(container.node);
              if (imgContainer) {
                  this.renderer.renderImage(container, bounds, container.borders, imgContainer);
              } else {
                  log("Error loading <" + container.node.nodeName + ">", container.node);
              }
              break;
          case "IMG":
              var imageContainer = this.images.get(container.node.src);
              if (imageContainer) {
                  this.renderer.renderImage(container, bounds, container.borders, imageContainer);
              } else {
                  log("Error loading <img>", container.node.src);
              }
              break;
          case "CANVAS":
              this.renderer.renderImage(container, bounds, container.borders, {image: container.node});
              break;
          case "SELECT":
          case "INPUT":
          case "TEXTAREA":
              this.paintFormValue(container);
              break;
          }
      }, this);
  };

  NodeParser.prototype.paintCheckbox = function(container) {
      var b = container.parseBounds();

      var size = Math.min(b.width, b.height);
      var bounds = {width: size - 1, height: size - 1, top: b.top, left: b.left};
      var r = [3, 3];
      var radius = [r, r, r, r];
      var borders = [1,1,1,1].map(function(w) {
          return {color: '#A5A5A5', width: w};
      });

      var borderPoints = calculateCurvePoints(bounds, radius, borders);

      this.renderer.clip(container.backgroundClip, function() {
          this.renderer.rectangle(bounds.left + 1, bounds.top + 1, bounds.width - 2, bounds.height - 2, "#DEDEDE");
          this.renderer.renderBorders(calculateBorders(borders, bounds, borderPoints, radius));
          if (container.node.checked) {
              this.renderer.font('#424242', 'normal', 'normal', 'bold', (size - 3) + "px", 'arial');
              this.renderer.text("\u2714", bounds.left + size / 6, bounds.top + size - 1);
          }
      }, this);
  };

  NodeParser.prototype.paintRadio = function(container) {
      var bounds = container.parseBounds();

      var size = Math.min(bounds.width, bounds.height) - 2;

      this.renderer.clip(container.backgroundClip, function() {
          this.renderer.circleStroke(bounds.left + 1, bounds.top + 1, size, '#DEDEDE', 1, '#A5A5A5');
          if (container.node.checked) {
              this.renderer.circle(Math.ceil(bounds.left + size / 4) + 1, Math.ceil(bounds.top + size / 4) + 1, Math.floor(size / 2), '#424242');
          }
      }, this);
  };

  NodeParser.prototype.paintFormValue = function(container) {
      if (container.getValue().length > 0) {
          var document = container.node.ownerDocument;
          var wrapper = document.createElement('html2canvaswrapper');
          var properties = ['lineHeight', 'textAlign', 'fontFamily', 'fontWeight', 'fontSize', 'color',
              'paddingLeft', 'paddingTop', 'paddingRight', 'paddingBottom',
              'width', 'height', 'borderLeftStyle', 'borderTopStyle', 'borderLeftWidth', 'borderTopWidth',
              'boxSizing', 'whiteSpace', 'wordWrap'];

          properties.forEach(function(property) {
              try {
                  wrapper.style[property] = container.css(property);
              } catch(e) {
                  // Older IE has issues with "border"
                  log("html2canvas: Parse: Exception caught in renderFormValue: " + e.message);
              }
          });
          var bounds = container.parseBounds();
          wrapper.style.position = "fixed";
          wrapper.style.left = bounds.left + "px";
          wrapper.style.top = bounds.top + "px";
          wrapper.textContent = container.getValue();
          document.body.appendChild(wrapper);
          this.paintText(new TextContainer(wrapper.firstChild, container));
          document.body.removeChild(wrapper);
      }
  };

  NodeParser.prototype.paintText = function(container) {
      container.applyTextTransform();
      var characters = window.html2canvas.punycode.ucs2.decode(container.node.data);
      var textList = (!this.options.letterRendering || noLetterSpacing(container)) && !hasUnicode(container.node.data) ? getWords(characters) : characters.map(function(character) {
          return window.html2canvas.punycode.ucs2.encode([character]);
      });

      var weight = container.parent.fontWeight();
      var size = container.parent.css('fontSize');
      var family = container.parent.css('fontFamily');
      var shadows = container.parent.parseTextShadows();

      this.renderer.font(container.parent.css('color'), container.parent.css('fontStyle'), container.parent.css('fontVariant'), weight, size, family);
      if (shadows.length) {
          // TODO: support multiple text shadows
          this.renderer.fontShadow(shadows[0].color, shadows[0].offsetX, shadows[0].offsetY, shadows[0].blur);
      } else {
          this.renderer.clearShadow();
      }

      this.renderer.clip(container.parent.clip, function() {
          textList.map(this.parseTextBounds(container), this).forEach(function(bounds, index) {
              if (bounds) {
              	if (bounds.left === undefined){
              		bounds.left = 0;
              	}
              	if (bounds.bottom === undefined){
              		bounds.bottom = 0;
              	}
                  this.renderer.text(textList[index], bounds.left, bounds.bottom);
                  this.renderTextDecoration(container.parent, bounds, this.fontMetrics.getMetrics(family, size));
                  if (index == 0 && container.parent.node.nodeName === 'LI'){
                  	this.renderBullet(container, bounds);
                  }
                  if (index == 0){
                  	this.renderAnnotation(container.parent, bounds)                	
                  }
              }
          }, this);
      }, this);
  };

  NodeParser.prototype.generateListNumber = {
  	listAlpha : function(number) {
  		var tmp = "", modulus;
  	
  		do {
  			modulus = number % 26;
  			tmp = String.fromCharCode((modulus) + 64) + tmp;
  			number = number / 26;
  		} while ((number * 26) > 26);
  	
  		return tmp;
  	},
  	
  	listRoman : function(number) {
  		var romanArray = [
  				"M", "CM", "D", "CD", "C", "XC", "L", "XL", "X", "IX", "V", "IV", "I"
  		], decimal = [
  				1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1
  		], roman = "", v, len = romanArray.length;
  	
  		if (number <= 0 || number >= 4000) {
  			return number;
  		}
  	
  		for (v = 0; v < len; v += 1) {
  			while (number >= decimal[v]) {
  				number -= decimal[v];
  				roman += romanArray[v];
  			}
  		}
  	
  		return roman;
  	}
  };


  NodeParser.prototype.listItemText = function (type, currentIndex) {
  	switch (type) {
  	case "decimal-leading-zero":
  		text = (currentIndex.toString().length === 1) ? currentIndex = "0" + currentIndex.toString() : currentIndex.toString();
  		break;
  	case "upper-roman":
  		text = this.generateListNumber.listRoman(currentIndex);
  		break;
  	case "lower-roman":
  		text = this.generateListNumber.listRoman(currentIndex).toLowerCase();
  		break;
  	case "lower-alpha":
  		text = this.generateListNumber.listAlpha(currentIndex).toLowerCase();
  		break;
  	case "upper-alpha":
  		text = this.generateListNumber.listAlpha(currentIndex);
  		break;
  	case "decimal":
  	default:
  		text = currentIndex;
  			break;
  	}

  	return text;
  }

  NodeParser.prototype.renderBullet = function(container, bounds){
  	var type = container.parent.css('listStyleType');
  	if (type === 'none'){
  		return;
  	}
  	var y = bounds.top + (bounds.bottom - bounds.top) / 2;
  	var c2d = this.renderer.canvas.getContext("2d");
  	var textWidth= c2d.measureText("M").width;
  	var size = textWidth/4;
  	var padding = textWidth * .75;
  	var x = bounds.left - padding;
  	switch(type){
  		case 'decimal':
  		case "decimal-leading-zero":
  		case 'upper-alpha':
  		case 'lower-alpha':
  		case 'upper-roman':
  		case 'lower-roman':
  			var li = container.parent;
  			var list = li.parent;
  			var nodeList = Array.prototype.slice.call(list.node.children);
  			var index = nodeList.indexOf(li.node) + 1;
  			
  			var value = this.listItemText(type, index);
  			value += '.';
  			var left = bounds.left - padding;
  			left -= c2d.measureText(value).width;
  			c2d.fillText(value, left, bounds.bottom);
  			break;
  		case 'square':
  			var size = textWidth/3;
  			x -= size;
  			y -= size / 2;
  			c2d.fillRect(x, y , size, size);
  			break;
  		case 'circle':
  			var size = textWidth/6;
  			x -= size;
  			c2d.beginPath();
  			c2d.arc(x, y, size, 0, Math.PI * 2);
  			c2d.closePath();
  			c2d.stroke();
  			break;
  		case 'disc':
  		default:
  			var size = textWidth/6;
  			x -= size;
  			c2d.beginPath();
  			c2d.arc(x, y, size, 0, Math.PI * 2);
  			c2d.closePath();
  			c2d.fill();
  			break;
  	}
  }

  NodeParser.prototype.renderTextDecoration = function(container, bounds, metrics) {
      switch(container.css("textDecoration").split(" ")[0]) {
      case "underline":
          // Draws a line at the baseline of the font
          // TODO As some browsers display the line as more than 1px if the font-size is big, need to take that into account both in position and size
          this.renderer.rectangle(bounds.left, Math.round(bounds.top + metrics.baseline + metrics.lineWidth), bounds.width, 1, container.css("color"));
          break;
      case "overline":
          this.renderer.rectangle(bounds.left, Math.round(bounds.top), bounds.width, 1, container.css("color"));
          break;
      case "line-through":
          // TODO try and find exact position for line-through
          this.renderer.rectangle(bounds.left, Math.ceil(bounds.top + metrics.middle + metrics.lineWidth), bounds.width, 1, container.css("color"));
          break;
      }
  };

  /**
   * Used for hyperlinks and PDF annotations
   * @param container
   * @param bounds
   */
  NodeParser.prototype.renderAnnotation = function(container, bounds) {
     if (container.node.nodeName === 'A'){
  	   var href = container.node.getAttribute('href');
  	   if (href){		   
  		   var annotations = this.options.canvas.annotations;
  		   if (annotations){
  			   annotations.createAnnotation(href, container.bounds);
  		   }
  	   }
     }
  };

  NodeParser.prototype.parseBorders = function(container) {
      var nodeBounds = container.parseBounds();
      var radius = getBorderRadiusData(container);
      var borders = ["Top", "Right", "Bottom", "Left"].map(function(side) {
          return {
              width: container.cssInt('border' + side + 'Width'),
              color: container.css('border' + side + 'Color'),
              args: null
          };
      });
      var borderPoints = calculateCurvePoints(nodeBounds, radius, borders);

      return {
          clip: this.parseBackgroundClip(container, borderPoints, borders, radius, nodeBounds),
          borders: calculateBorders(borders, nodeBounds, borderPoints, radius)
      };
  };

  function calculateBorders(borders, nodeBounds, borderPoints, radius) {
      return borders.map(function(border, borderSide) {
          if (border.width > 0) {
              var bx = nodeBounds.left;
              var by = nodeBounds.top;
              var bw = nodeBounds.width;
              var bh = nodeBounds.height - (borders[2].width);

              switch(borderSide) {
              case 0:
                  // top border
                  bh = borders[0].width;
                  border.args = drawSide({
                          c1: [bx, by],
                          c2: [bx + bw, by],
                          c3: [bx + bw - borders[1].width, by + bh],
                          c4: [bx + borders[3].width, by + bh]
                      }, radius[0], radius[1],
                      borderPoints.topLeftOuter, borderPoints.topLeftInner, borderPoints.topRightOuter, borderPoints.topRightInner);
                  break;
              case 1:
                  // right border
                  bx = nodeBounds.left + nodeBounds.width - (borders[1].width);
                  bw = borders[1].width;

                  border.args = drawSide({
                          c1: [bx + bw, by],
                          c2: [bx + bw, by + bh + borders[2].width],
                          c3: [bx, by + bh],
                          c4: [bx, by + borders[0].width]
                      }, radius[1], radius[2],
                      borderPoints.topRightOuter, borderPoints.topRightInner, borderPoints.bottomRightOuter, borderPoints.bottomRightInner);
                  break;
              case 2:
                  // bottom border
                  by = (by + nodeBounds.height) - (borders[2].width);
                  bh = borders[2].width;
                  border.args = drawSide({
                          c1: [bx + bw, by + bh],
                          c2: [bx, by + bh],
                          c3: [bx + borders[3].width, by],
                          c4: [bx + bw - borders[3].width, by]
                      }, radius[2], radius[3],
                      borderPoints.bottomRightOuter, borderPoints.bottomRightInner, borderPoints.bottomLeftOuter, borderPoints.bottomLeftInner);
                  break;
              case 3:
                  // left border
                  bw = borders[3].width;
                  border.args = drawSide({
                          c1: [bx, by + bh + borders[2].width],
                          c2: [bx, by],
                          c3: [bx + bw, by + borders[0].width],
                          c4: [bx + bw, by + bh]
                      }, radius[3], radius[0],
                      borderPoints.bottomLeftOuter, borderPoints.bottomLeftInner, borderPoints.topLeftOuter, borderPoints.topLeftInner);
                  break;
              }
          }
          return border;
      });
  }

  NodeParser.prototype.parseBackgroundClip = function(container, borderPoints, borders, radius, bounds) {
      var backgroundClip = container.css('backgroundClip'),
          borderArgs = [];

      switch(backgroundClip) {
      case "content-box":
      case "padding-box":
          parseCorner(borderArgs, radius[0], radius[1], borderPoints.topLeftInner, borderPoints.topRightInner, bounds.left + borders[3].width, bounds.top + borders[0].width);
          parseCorner(borderArgs, radius[1], radius[2], borderPoints.topRightInner, borderPoints.bottomRightInner, bounds.left + bounds.width - borders[1].width, bounds.top + borders[0].width);
          parseCorner(borderArgs, radius[2], radius[3], borderPoints.bottomRightInner, borderPoints.bottomLeftInner, bounds.left + bounds.width - borders[1].width, bounds.top + bounds.height - borders[2].width);
          parseCorner(borderArgs, radius[3], radius[0], borderPoints.bottomLeftInner, borderPoints.topLeftInner, bounds.left + borders[3].width, bounds.top + bounds.height - borders[2].width);
          break;

      default:
          parseCorner(borderArgs, radius[0], radius[1], borderPoints.topLeftOuter, borderPoints.topRightOuter, bounds.left, bounds.top);
          parseCorner(borderArgs, radius[1], radius[2], borderPoints.topRightOuter, borderPoints.bottomRightOuter, bounds.left + bounds.width, bounds.top);
          parseCorner(borderArgs, radius[2], radius[3], borderPoints.bottomRightOuter, borderPoints.bottomLeftOuter, bounds.left + bounds.width, bounds.top + bounds.height);
          parseCorner(borderArgs, radius[3], radius[0], borderPoints.bottomLeftOuter, borderPoints.topLeftOuter, bounds.left, bounds.top + bounds.height);
          break;
      }

      return borderArgs;
  };

  function getCurvePoints(x, y, r1, r2) {
      var kappa = 4 * ((Math.sqrt(2) - 1) / 3);
      var ox = (r1) * kappa, // control point offset horizontal
          oy = (r2) * kappa, // control point offset vertical
          xm = x + r1, // x-middle
          ym = y + r2; // y-middle
      return {
          topLeft: bezierCurve({x: x, y: ym}, {x: x, y: ym - oy}, {x: xm - ox, y: y}, {x: xm, y: y}),
          topRight: bezierCurve({x: x, y: y}, {x: x + ox,y: y}, {x: xm, y: ym - oy}, {x: xm, y: ym}),
          bottomRight: bezierCurve({x: xm, y: y}, {x: xm, y: y + oy}, {x: x + ox, y: ym}, {x: x, y: ym}),
          bottomLeft: bezierCurve({x: xm, y: ym}, {x: xm - ox, y: ym}, {x: x, y: y + oy}, {x: x, y:y})
      };
  }

  function calculateCurvePoints(bounds, borderRadius, borders) {
      var x = bounds.left,
          y = bounds.top,
          width = bounds.width,
          height = bounds.height,

          tlh = borderRadius[0][0],
          tlv = borderRadius[0][1],
          trh = borderRadius[1][0],
          trv = borderRadius[1][1],
          brh = borderRadius[2][0],
          brv = borderRadius[2][1],
          blh = borderRadius[3][0],
          blv = borderRadius[3][1];

      var topWidth = width - trh,
          rightHeight = height - brv,
          bottomWidth = width - brh,
          leftHeight = height - blv;

      return {
          topLeftOuter: getCurvePoints(x, y, tlh, tlv).topLeft.subdivide(0.5),
          topLeftInner: getCurvePoints(x + borders[3].width, y + borders[0].width, Math.max(0, tlh - borders[3].width), Math.max(0, tlv - borders[0].width)).topLeft.subdivide(0.5),
          topRightOuter: getCurvePoints(x + topWidth, y, trh, trv).topRight.subdivide(0.5),
          topRightInner: getCurvePoints(x + Math.min(topWidth, width + borders[3].width), y + borders[0].width, (topWidth > width + borders[3].width) ? 0 :trh - borders[3].width, trv - borders[0].width).topRight.subdivide(0.5),
          bottomRightOuter: getCurvePoints(x + bottomWidth, y + rightHeight, brh, brv).bottomRight.subdivide(0.5),
          bottomRightInner: getCurvePoints(x + Math.min(bottomWidth, width - borders[3].width), y + Math.min(rightHeight, height + borders[0].width), Math.max(0, brh - borders[1].width),  brv - borders[2].width).bottomRight.subdivide(0.5),
          bottomLeftOuter: getCurvePoints(x, y + leftHeight, blh, blv).bottomLeft.subdivide(0.5),
          bottomLeftInner: getCurvePoints(x + borders[3].width, y + leftHeight, Math.max(0, blh - borders[3].width), blv - borders[2].width).bottomLeft.subdivide(0.5)
      };
  }

  function bezierCurve(start, startControl, endControl, end) {
      var lerp = function (a, b, t) {
          return {
              x: a.x + (b.x - a.x) * t,
              y: a.y + (b.y - a.y) * t
          };
      };

      return {
          start: start,
          startControl: startControl,
          endControl: endControl,
          end: end,
          subdivide: function(t) {
              var ab = lerp(start, startControl, t),
                  bc = lerp(startControl, endControl, t),
                  cd = lerp(endControl, end, t),
                  abbc = lerp(ab, bc, t),
                  bccd = lerp(bc, cd, t),
                  dest = lerp(abbc, bccd, t);
              return [bezierCurve(start, ab, abbc, dest), bezierCurve(dest, bccd, cd, end)];
          },
          curveTo: function(borderArgs) {
              borderArgs.push(["bezierCurve", startControl.x, startControl.y, endControl.x, endControl.y, end.x, end.y]);
          },
          curveToReversed: function(borderArgs) {
              borderArgs.push(["bezierCurve", endControl.x, endControl.y, startControl.x, startControl.y, start.x, start.y]);
          }
      };
  }

  function drawSide(borderData, radius1, radius2, outer1, inner1, outer2, inner2) {
      var borderArgs = [];

      if (radius1[0] > 0 || radius1[1] > 0) {
          borderArgs.push(["line", outer1[1].start.x, outer1[1].start.y]);
          outer1[1].curveTo(borderArgs);
      } else {
          borderArgs.push([ "line", borderData.c1[0], borderData.c1[1]]);
      }

      if (radius2[0] > 0 || radius2[1] > 0) {
          borderArgs.push(["line", outer2[0].start.x, outer2[0].start.y]);
          outer2[0].curveTo(borderArgs);
          borderArgs.push(["line", inner2[0].end.x, inner2[0].end.y]);
          inner2[0].curveToReversed(borderArgs);
      } else {
          borderArgs.push(["line", borderData.c2[0], borderData.c2[1]]);
          borderArgs.push(["line", borderData.c3[0], borderData.c3[1]]);
      }

      if (radius1[0] > 0 || radius1[1] > 0) {
          borderArgs.push(["line", inner1[1].end.x, inner1[1].end.y]);
          inner1[1].curveToReversed(borderArgs);
      } else {
          borderArgs.push(["line", borderData.c4[0], borderData.c4[1]]);
      }

      return borderArgs;
  }

  function parseCorner(borderArgs, radius1, radius2, corner1, corner2, x, y) {
      if (radius1[0] > 0 || radius1[1] > 0) {
          borderArgs.push(["line", corner1[0].start.x, corner1[0].start.y]);
          corner1[0].curveTo(borderArgs);
          corner1[1].curveTo(borderArgs);
      } else {
          borderArgs.push(["line", x, y]);
      }

      if (radius2[0] > 0 || radius2[1] > 0) {
          borderArgs.push(["line", corner2[0].start.x, corner2[0].start.y]);
      }
  }

  function negativeZIndex(container) {
      return container.cssInt("zIndex") < 0;
  }

  function positiveZIndex(container) {
      return container.cssInt("zIndex") > 0;
  }

  function zIndex0(container) {
      return container.cssInt("zIndex") === 0;
  }

  function inlineLevel(container) {
      return ["inline", "inline-block", "inline-table"].indexOf(container.css("display")) !== -1;
  }

  function isStackingContext(container) {
      return (container instanceof StackingContext);
  }

  function hasText(container) {
      return container.node.data.trim().length > 0;
  }

  function noLetterSpacing(container) {
      return (/^(normal|none|0px)$/.test(container.parent.css("letterSpacing")));
  }

  function getBorderRadiusData(container) {
      return ["TopLeft", "TopRight", "BottomRight", "BottomLeft"].map(function(side) {
          var value = container.css('border' + side + 'Radius');
          var arr = value.split(" ");
          if (arr.length <= 1) {
              arr[1] = arr[0];
          }
          return arr.map(asInt);
      });
  }

  function renderableNode(node) {
      return (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.ELEMENT_NODE);
  }

  function isPositionedForStacking(container) {
      var position = container.css("position");
      var zIndex = (["absolute", "relative", "fixed"].indexOf(position) !== -1) ? container.css("zIndex") : "auto";
      return zIndex !== "auto";
  }

  function isPositioned(container) {
      return container.css("position") !== "static";
  }

  function isFloating(container) {
      return container.css("float") !== "none";
  }

  function isInlineBlock(container) {
      return ["inline-block", "inline-table"].indexOf(container.css("display")) !== -1;
  }

  function not(callback) {
      var context = this;
      return function() {
          return !callback.apply(context, arguments);
      };
  }

  function isElement(container) {
      return container.node.nodeType === Node.ELEMENT_NODE;
  }

  function isPseudoElement(container) {
      return container.isPseudoElement === true;
  }

  function isTextNode(container) {
      return container.node.nodeType === Node.TEXT_NODE;
  }

  function zIndexSort(contexts) {
      return function(a, b) {
          return (a.cssInt("zIndex") + (contexts.indexOf(a) / contexts.length)) - (b.cssInt("zIndex") + (contexts.indexOf(b) / contexts.length));
      };
  }

  function hasOpacity(container) {
      return container.getOpacity() < 1;
  }

  function bind(callback, context) {
      return function() {
          return callback.apply(context, arguments);
      };
  }

  function asInt(value) {
      return parseInt(value, 10);
  }

  function getWidth(border) {
      return border.width;
  }

  function nonIgnoredElement(nodeContainer) {
      return (nodeContainer.node.nodeType !== Node.ELEMENT_NODE || ["SCRIPT", "HEAD", "TITLE", "OBJECT", "BR", "OPTION"].indexOf(nodeContainer.node.nodeName) === -1);
  }

  function flatten(arrays) {
      return [].concat.apply([], arrays);
  }

  function stripQuotes(content) {
      var first = content.substr(0, 1);
      return (first === content.substr(content.length - 1) && first.match(/'|"/)) ? content.substr(1, content.length - 2) : content;
  }

  function getWords(characters) {
      var words = [], i = 0, onWordBoundary = false, word;
      while(characters.length) {
          if (isWordBoundary(characters[i]) === onWordBoundary) {
              word = characters.splice(0, i);
              if (word.length) {
                  words.push(window.html2canvas.punycode.ucs2.encode(word));
              }
              onWordBoundary =! onWordBoundary;
              i = 0;
          } else {
              i++;
          }

          if (i >= characters.length) {
              word = characters.splice(0, i);
              if (word.length) {
                  words.push(window.html2canvas.punycode.ucs2.encode(word));
              }
          }
      }
      return words;
  }

  function isWordBoundary(characterCode) {
      return [
          32, // <space>
          13, // \r
          10, // \n
          9, // \t
          45 // -
      ].indexOf(characterCode) !== -1;
  }

  function hasUnicode(string) {
      return (/[^\u0000-\u00ff]/).test(string);
  }

  function Proxy(src, proxyUrl, document) {
      var callback = createCallback(supportsCORS);
      var url = createProxyUrl(proxyUrl, src, callback);

      return supportsCORS ? XHR(url) : (jsonp(document, url, callback).then(function(response) {
          return decode64(response.content);
      }));
  }
  var proxyCount = 0;

  var supportsCORS = ('withCredentials' in new XMLHttpRequest());
  var supportsCORSImage = ('crossOrigin' in new Image());

  function ProxyURL(src, proxyUrl, document) {
      var callback = createCallback(supportsCORSImage);
      var url = createProxyUrl(proxyUrl, src, callback);
      return (supportsCORSImage ? Promise.resolve(url) : jsonp(document, url, callback).then(function(response) {
          return "data:" + response.type + ";base64," + response.content;
      }));
  }

  function jsonp(document, url, callback) {
      return new Promise(function(resolve, reject) {
          var s = document.createElement("script");
          var cleanup = function() {
              delete window.html2canvas.proxy[callback];
              document.body.removeChild(s);
          };
          window.html2canvas.proxy[callback] = function(response) {
              cleanup();
              resolve(response);
          };
          s.src = url;
          s.onerror = function(e) {
              cleanup();
              reject(e);
          };
          document.body.appendChild(s);
      });
  }

  function createCallback(useCORS) {
      return !useCORS ? "html2canvas_" + Date.now() + "_" + (++proxyCount) + "_" + Math.round(Math.random() * 100000) : "";
  }

  function createProxyUrl(proxyUrl, src, callback) {
      return proxyUrl + "?url=" + encodeURIComponent(src) + (callback.length ? "&callback=html2canvas.proxy." + callback : "");
  }

  function ProxyImageContainer(src, proxy) {
      var script = document.createElement("script");
      var link = document.createElement("a");
      link.href = src;
      src = link.href;
      this.src = src;
      this.image = new Image();
      var self = this;
      this.promise = new Promise(function(resolve, reject) {
          self.image.crossOrigin = "Anonymous";
          self.image.onload = resolve;
          self.image.onerror = reject;

          new ProxyURL(src, proxy, document).then(function(url) {
              self.image.src = url;
          })['catch'](reject);
      });
  }

  function PseudoElementContainer(node, parent, type) {
      NodeContainer.call(this, node, parent);
      this.isPseudoElement = true;
      this.before = type === ":before";
  }

  PseudoElementContainer.prototype.cloneTo = function(stack) {
      PseudoElementContainer.prototype.cloneTo.call(this, stack);
      stack.isPseudoElement = true;
      stack.before = this.before;
  };

  PseudoElementContainer.prototype = Object.create(NodeContainer.prototype);

  PseudoElementContainer.prototype.appendToDOM = function() {
      if (this.before) {
          this.parent.node.insertBefore(this.node, this.parent.node.firstChild);
      } else {
          this.parent.node.appendChild(this.node);
      }
      this.parent.node.className += " " + this.getHideClass();
  };

  PseudoElementContainer.prototype.cleanDOM = function() {
      this.node.parentNode.removeChild(this.node);
      this.parent.node.className = this.parent.node.className.replace(this.getHideClass(), "");
  };

  PseudoElementContainer.prototype.getHideClass = function() {
      return this["PSEUDO_HIDE_ELEMENT_CLASS_" + (this.before ? "BEFORE" : "AFTER")];
  };

  PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_BEFORE = "___html2canvas___pseudoelement_before";
  PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_AFTER = "___html2canvas___pseudoelement_after";

  function Renderer(width, height, images, options, document) {
      this.width = width;
      this.height = height;
      this.images = images;
      this.options = options;
      this.document = document;
  }

  Renderer.prototype.renderImage = function(container, bounds, borderData, imageContainer) {
      var paddingLeft = container.cssInt('paddingLeft'),
          paddingTop = container.cssInt('paddingTop'),
          paddingRight = container.cssInt('paddingRight'),
          paddingBottom = container.cssInt('paddingBottom'),
          borders = borderData.borders;

      var width = bounds.width - (borders[1].width + borders[3].width + paddingLeft + paddingRight);
      var height = bounds.height - (borders[0].width + borders[2].width + paddingTop + paddingBottom);
      this.drawImage(
          imageContainer,
          0,
          0,
          imageContainer.image.width || width,
          imageContainer.image.height || height,
          bounds.left + paddingLeft + borders[3].width,
          bounds.top + paddingTop + borders[0].width,
          width,
          height
      );
  };

  Renderer.prototype.renderBackground = function(container, bounds, borderData) {
      if (bounds.height > 0 && bounds.width > 0) {
          this.renderBackgroundColor(container, bounds);
          this.renderBackgroundImage(container, bounds, borderData);
      }
  };

  Renderer.prototype.renderBackgroundColor = function(container, bounds) {
      var color = container.css("backgroundColor");
      if (!this.isTransparent(color)) {
          this.rectangle(bounds.left, bounds.top, bounds.width, bounds.height, container.css("backgroundColor"));
      }
  };

  Renderer.prototype.renderBorders = function(borders) {
      borders.forEach(this.renderBorder, this);
  };

  Renderer.prototype.renderBorder = function(data) {
      if (!this.isTransparent(data.color) && data.args !== null) {
          this.drawShape(data.args, data.color);
      }
  };

  Renderer.prototype.renderBackgroundImage = function(container, bounds, borderData) {
      var backgroundImages = container.parseBackgroundImages();
      backgroundImages.reverse().forEach(function(backgroundImage, index, arr) {
          switch(backgroundImage.method) {
              case "url":
                  var image = this.images.get(backgroundImage.args[0]);
                  if (image) {
                      this.renderBackgroundRepeating(container, bounds, image, arr.length - (index+1), borderData);
                  } else {
                      log("Error loading background-image", backgroundImage.args[0]);
                  }
                  break;
              case "linear-gradient":
              case "gradient":
                  var gradientImage = this.images.get(backgroundImage.value);
                  if (gradientImage) {
                      this.renderBackgroundGradient(gradientImage, bounds, borderData);
                  } else {
                      log("Error loading background-image", backgroundImage.args[0]);
                  }
                  break;
              case "none":
                  break;
              default:
                  log("Unknown background-image type", backgroundImage.args[0]);
          }
      }, this);
  };

  Renderer.prototype.renderBackgroundRepeating = function(container, bounds, imageContainer, index, borderData) {
      var size = container.parseBackgroundSize(bounds, imageContainer.image, index);
      var position = container.parseBackgroundPosition(bounds, imageContainer.image, index, size);
      var repeat = container.parseBackgroundRepeat(index);
      switch (repeat) {
      case "repeat-x":
      case "repeat no-repeat":
          this.backgroundRepeatShape(imageContainer, position, size, bounds, bounds.left + borderData[3], bounds.top + position.top + borderData[0], 99999, size.height, borderData);
          break;
      case "repeat-y":
      case "no-repeat repeat":
          this.backgroundRepeatShape(imageContainer, position, size, bounds, bounds.left + position.left + borderData[3], bounds.top + borderData[0], size.width, 99999, borderData);
          break;
      case "no-repeat":
          this.backgroundRepeatShape(imageContainer, position, size, bounds, bounds.left + position.left + borderData[3], bounds.top + position.top + borderData[0], size.width, size.height, borderData);
          break;
      default:
          this.renderBackgroundRepeat(imageContainer, position, size, {top: bounds.top, left: bounds.left}, borderData[3], borderData[0]);
          break;
      }
  };

  Renderer.prototype.isTransparent = function(color) {
      return (!color || color === "transparent" || color === "rgba(0, 0, 0, 0)");
  };

  function StackingContext(hasOwnStacking, opacity, element, parent) {
      NodeContainer.call(this, element, parent);
      this.ownStacking = hasOwnStacking;
      this.contexts = [];
      this.children = [];
      this.opacity = (this.parent ? this.parent.stack.opacity : 1) * opacity;
  }

  StackingContext.prototype = Object.create(NodeContainer.prototype);

  StackingContext.prototype.getParentStack = function(context) {
      var parentStack = (this.parent) ? this.parent.stack : null;
      return parentStack ? (parentStack.ownStacking ? parentStack : parentStack.getParentStack(context)) : context.stack;
  };

  function Support(document) {
      this.rangeBounds = this.testRangeBounds(document);
      this.cors = this.testCORS();
      this.svg = this.testSVG();
  }

  Support.prototype.testRangeBounds = function(document) {
      var range, testElement, rangeBounds, rangeHeight, support = false;

      if (document.createRange) {
          range = document.createRange();
          if (range.getBoundingClientRect) {
              testElement = document.createElement('boundtest');
              testElement.style.height = "123px";
              testElement.style.display = "block";
              document.body.appendChild(testElement);

              range.selectNode(testElement);
              rangeBounds = range.getBoundingClientRect();
              rangeHeight = rangeBounds.height;

              if (rangeHeight === 123) {
                  support = true;
              }
              document.body.removeChild(testElement);
          }
      }

      return support;
  };

  Support.prototype.testCORS = function() {
      return typeof((new Image()).crossOrigin) !== "undefined";
  };

  Support.prototype.testSVG = function() {
      var img = new Image();
      var canvas = document.createElement("canvas");
      var ctx =  canvas.getContext("2d");
      img.src = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'></svg>";

      try {
          ctx.drawImage(img, 0, 0);
          canvas.toDataURL();
      } catch(e) {
          return false;
      }
      return true;
  };

  function SVGContainer(src) {
      this.src = src;
      this.image = null;
      var self = this;

      this.promise = this.hasFabric().then(function() {
          return (self.isInline(src) ? Promise.resolve(self.inlineFormatting(src)) : XHR(src));
      }).then(function(svg) {
          return new Promise(function(resolve) {
              html2canvas.fabric.loadSVGFromString(svg, self.createCanvas.call(self, resolve));
          });
      });
  }

  SVGContainer.prototype.hasFabric = function() {
      return !html2canvas.fabric ? Promise.reject(new Error("html2canvas.svg.js is not loaded, cannot render svg")) : Promise.resolve();
  };

  SVGContainer.prototype.inlineFormatting = function(src) {
      return (/^data:image\/svg\+xml;base64,/.test(src)) ? this.decode64(this.removeContentType(src)) : this.removeContentType(src);
  };

  SVGContainer.prototype.removeContentType = function(src) {
      return src.replace(/^data:image\/svg\+xml(;base64)?,/,'');
  };

  SVGContainer.prototype.isInline = function(src) {
      return (/^data:image\/svg\+xml/i.test(src));
  };

  SVGContainer.prototype.createCanvas = function(resolve) {
      var self = this;
      return function (objects, options) {
          var canvas = new html2canvas.fabric.StaticCanvas('c');
          self.image = canvas.lowerCanvasEl;
          canvas
              .setWidth(options.width)
              .setHeight(options.height)
              .add(html2canvas.fabric.util.groupSVGElements(objects, options))
              .renderAll();
          resolve(canvas.lowerCanvasEl);
      };
  };

  SVGContainer.prototype.decode64 = function(str) {
      return (typeof(window.atob) === "function") ? window.atob(str) : decode64(str);
  };

  /*
   * base64-arraybuffer
   * https://github.com/niklasvh/base64-arraybuffer
   *
   * Copyright (c) 2012 Niklas von Hertzen
   * Licensed under the MIT license.
   */

  function decode64(base64) {
      var chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
      var len = base64.length, i, encoded1, encoded2, encoded3, encoded4, byte1, byte2, byte3;

      var output = "";

      for (i = 0; i < len; i+=4) {
          encoded1 = chars.indexOf(base64[i]);
          encoded2 = chars.indexOf(base64[i+1]);
          encoded3 = chars.indexOf(base64[i+2]);
          encoded4 = chars.indexOf(base64[i+3]);

          byte1 = (encoded1 << 2) | (encoded2 >> 4);
          byte2 = ((encoded2 & 15) << 4) | (encoded3 >> 2);
          byte3 = ((encoded3 & 3) << 6) | encoded4;
          if (encoded3 === 64) {
              output += String.fromCharCode(byte1);
          } else if (encoded4 === 64 || encoded4 === -1) {
              output += String.fromCharCode(byte1, byte2);
          } else{
              output += String.fromCharCode(byte1, byte2, byte3);
          }
      }

      return output;
  }

  function SVGNodeContainer(node, native) {
      this.src = node;
      this.image = null;
      var self = this;

      this.promise = native ? new Promise(function(resolve, reject) {
          self.image = new Image();
          self.image.onload = resolve;
          self.image.onerror = reject;
          self.image.src = "data:image/svg+xml," + (new XMLSerializer()).serializeToString(node);
          if (self.image.complete === true) {
              resolve(self.image);
          }
      }) : this.hasFabric().then(function() {
          return new Promise(function(resolve) {
              html2canvas.fabric.parseSVGDocument(node, self.createCanvas.call(self, resolve));
          });
      });
  }

  SVGNodeContainer.prototype = Object.create(SVGContainer.prototype);

  function TextContainer(node, parent) {
      NodeContainer.call(this, node, parent);
  }

  TextContainer.prototype = Object.create(NodeContainer.prototype);

  TextContainer.prototype.applyTextTransform = function() {
      this.node.data = this.transform(this.parent.css("textTransform"));
  };

  TextContainer.prototype.transform = function(transform) {
      var text = this.node.data;
      switch(transform){
          case "lowercase":
              return text.toLowerCase();
          case "capitalize":
              return text.replace(/(^|\s|:|-|\(|\))([a-z])/g, capitalize);
          case "uppercase":
              return text.toUpperCase();
          default:
              return text;
      }
  };

  function capitalize(m, p1, p2) {
      if (m.length > 0) {
          return p1 + p2.toUpperCase();
      }
  }

  function WebkitGradientContainer(imageData) {
      GradientContainer.apply(this, arguments);
      this.type = (imageData.args[0] === "linear") ? this.TYPES.LINEAR : this.TYPES.RADIAL;
  }

  WebkitGradientContainer.prototype = Object.create(GradientContainer.prototype);

  function XHR(url) {
      return new Promise(function(resolve, reject) {
          var xhr = new XMLHttpRequest();
          xhr.open('GET', url);

          xhr.onload = function() {
              if (xhr.status === 200) {
                  resolve(xhr.responseText);
              } else {
                  reject(new Error(xhr.statusText));
              }
          };

          xhr.onerror = function() {
              reject(new Error("Network Error"));
          };

          xhr.send();
      });
  }

  function CanvasRenderer(width, height) {
      Renderer.apply(this, arguments);
      this.canvas = this.options.canvas || this.document.createElement("canvas");
      if (!this.options.canvas) {
          this.canvas.width = width;
          this.canvas.height = height;
      }
      this.ctx = this.canvas.getContext("2d");
      if (this.options.background) {
          this.rectangle(0, 0, width, height, this.options.background);
      }
      this.taintCtx = this.document.createElement("canvas").getContext("2d");
      this.ctx.textBaseline = "bottom";
      this.variables = {};
      log("Initialized CanvasRenderer with size", width, "x", height);
  }

  CanvasRenderer.prototype = Object.create(Renderer.prototype);

  CanvasRenderer.prototype.setFillStyle = function(color) {
      this.ctx.fillStyle = color;
      return this.ctx;
  };

  CanvasRenderer.prototype.rectangle = function(left, top, width, height, color) {
      this.setFillStyle(color).fillRect(left, top, width, height);
  };

  CanvasRenderer.prototype.circle = function(left, top, size, color) {
      this.setFillStyle(color);
      this.ctx.beginPath();
      this.ctx.arc(left + size / 2, top + size / 2, size / 2, 0, Math.PI*2, true);
      this.ctx.closePath();
      this.ctx.fill();
  };

  CanvasRenderer.prototype.circleStroke = function(left, top, size, color, stroke, strokeColor) {
      this.circle(left, top, size, color);
      this.ctx.strokeStyle = strokeColor;
      this.ctx.stroke();
  };

  CanvasRenderer.prototype.drawShape = function(shape, color) {
      this.shape(shape);
      this.setFillStyle(color).fill();
  };

  CanvasRenderer.prototype.taints = function(imageContainer) {
      if (imageContainer.tainted === null) {
          this.taintCtx.drawImage(imageContainer.image, 0, 0);
          try {
              this.taintCtx.getImageData(0, 0, 1, 1);
              imageContainer.tainted = false;
          } catch(e) {
              this.taintCtx = document.createElement("canvas").getContext("2d");
              imageContainer.tainted = true;
          }
      }

      return imageContainer.tainted;
  };

  CanvasRenderer.prototype.drawImage = function(imageContainer, sx, sy, sw, sh, dx, dy, dw, dh) {
      if (!this.taints(imageContainer) || this.options.allowTaint) {
          this.ctx.drawImage(imageContainer.image, sx, sy, sw, sh, dx, dy, dw, dh);
      }
  };

  CanvasRenderer.prototype.clip = function(shapes, callback, context) {
      this.ctx.save();
      shapes.filter(hasEntries).forEach(function(shape) {
          this.shape(shape).clip();
      }, this);
      callback.call(context);
      this.ctx.restore();
  };

  CanvasRenderer.prototype.shape = function(shape) {
      this.ctx.beginPath();
      shape.forEach(function(point, index) {
          if (point[0] === "rect") {
              this.ctx.rect.apply(this.ctx, point.slice(1));
          } else {
              this.ctx[(index === 0) ? "moveTo" : point[0] + "To" ].apply(this.ctx, point.slice(1));
          }
      }, this);
      this.ctx.closePath();
      return this.ctx;
  };

  CanvasRenderer.prototype.font = function(color, style, variant, weight, size, family) {
      this.setFillStyle(color).font = [style, variant, weight, size, family].join(" ").split(",")[0];
  };

  CanvasRenderer.prototype.fontShadow = function(color, offsetX, offsetY, blur) {
      this.setVariable("shadowColor", color)
          .setVariable("shadowOffsetY", offsetX)
          .setVariable("shadowOffsetX", offsetY)
          .setVariable("shadowBlur", blur);
  };

  CanvasRenderer.prototype.clearShadow = function() {
      this.setVariable("shadowColor", "rgba(0,0,0,0)");
  };

  CanvasRenderer.prototype.setOpacity = function(opacity) {
      this.ctx.globalAlpha = opacity;
  };

  CanvasRenderer.prototype.setTransform = function(transform) {
      this.ctx.translate(transform.origin[0], transform.origin[1]);
      this.ctx.transform.apply(this.ctx, transform.matrix);
      this.ctx.translate(-transform.origin[0], -transform.origin[1]);
  };

  CanvasRenderer.prototype.setVariable = function(property, value) {
      if (this.variables[property] !== value) {
          this.variables[property] = this.ctx[property] = value;
      }

      return this;
  };

  CanvasRenderer.prototype.text = function(text, left, bottom) {
  		this.ctx.fillText(text, left, bottom);		
  };

  CanvasRenderer.prototype.backgroundRepeatShape = function(imageContainer, backgroundPosition, size, bounds, left, top, width, height, borderData) {
      var shape = [
          ["line", Math.round(left), Math.round(top)],
          ["line", Math.round(left + width), Math.round(top)],
          ["line", Math.round(left + width), Math.round(height + top)],
          ["line", Math.round(left), Math.round(height + top)]
      ];
      this.clip([shape], function() {
          this.renderBackgroundRepeat(imageContainer, backgroundPosition, size, bounds, borderData[3], borderData[0]);
      }, this);
  };

  CanvasRenderer.prototype.renderBackgroundRepeat = function(imageContainer, backgroundPosition, size, bounds, borderLeft, borderTop) {
      var offsetX = Math.round(bounds.left + backgroundPosition.left + borderLeft), offsetY = Math.round(bounds.top + backgroundPosition.top + borderTop);
      this.setFillStyle(this.ctx.createPattern(this.resizeImage(imageContainer, size), "repeat"));
      this.ctx.translate(offsetX, offsetY);
      this.ctx.fill();
      this.ctx.translate(-offsetX, -offsetY);
  };

  CanvasRenderer.prototype.renderBackgroundGradient = function(gradientImage, bounds) {
      if (gradientImage instanceof LinearGradientContainer) {
          var gradient = this.ctx.createLinearGradient(
              bounds.left + bounds.width * gradientImage.x0,
              bounds.top + bounds.height * gradientImage.y0,
              bounds.left +  bounds.width * gradientImage.x1,
              bounds.top +  bounds.height * gradientImage.y1);
          gradientImage.colorStops.forEach(function(colorStop) {
              gradient.addColorStop(colorStop.stop, colorStop.color);
          });
          this.rectangle(bounds.left, bounds.top, bounds.width, bounds.height, gradient);
      }
  };

  CanvasRenderer.prototype.resizeImage = function(imageContainer, size) {
      var image = imageContainer.image;
      if(image.width === size.width && image.height === size.height) {
          return image;
      }

      var ctx, canvas = document.createElement('canvas');
      canvas.width = size.width;
      canvas.height = size.height;
      ctx = canvas.getContext("2d");
      ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, size.width, size.height );
      return canvas;
  };

  function hasEntries(array) {
      return array.length > 0;
  }

  }).call({}, window, document);

  // Generated by CoffeeScript 1.4.0

  /*
  # PNG.js
  # Copyright (c) 2011 Devon Govett
  # MIT LICENSE
  # 
  # 
  */


  (function(global) {
    var PNG;

    PNG = (function() {
      var APNG_BLEND_OP_OVER, APNG_BLEND_OP_SOURCE, APNG_DISPOSE_OP_BACKGROUND, APNG_DISPOSE_OP_NONE, APNG_DISPOSE_OP_PREVIOUS, makeImage, scratchCanvas, scratchCtx;

      PNG.load = function(url, canvas, callback) {
        var xhr,
          _this = this;
        if (typeof canvas === 'function') {
          callback = canvas;
        }
        xhr = new XMLHttpRequest;
        xhr.open("GET", url, true);
        xhr.responseType = "arraybuffer";
        xhr.onload = function() {
          var data, png;
          data = new Uint8Array(xhr.response || xhr.mozResponseArrayBuffer);
          png = new PNG(data);
          if (typeof (canvas != null ? canvas.getContext : void 0) === 'function') {
            png.render(canvas);
          }
          return typeof callback === "function" ? callback(png) : void 0;
        };
        return xhr.send(null);
      };

      APNG_DISPOSE_OP_NONE = 0;

      APNG_DISPOSE_OP_BACKGROUND = 1;

      APNG_DISPOSE_OP_PREVIOUS = 2;

      APNG_BLEND_OP_SOURCE = 0;

      APNG_BLEND_OP_OVER = 1;

      function PNG(data) {
        var chunkSize, colors, palLen, delayDen, delayNum, frame, i, index, key, section, palShort, text, _i, _j, _ref;
        this.data = data;
        this.pos = 8;
        this.palette = [];
        this.imgData = [];
        this.transparency = {};
        this.animation = null;
        this.text = {};
        frame = null;
        while (true) {
          chunkSize = this.readUInt32();
          section = ((function() {
            var _i, _results;
            _results = [];
            for (i = _i = 0; _i < 4; i = ++_i) {
              _results.push(String.fromCharCode(this.data[this.pos++]));
            }
            return _results;
          }).call(this)).join('');
          switch (section) {
            case 'IHDR':
              this.width = this.readUInt32();
              this.height = this.readUInt32();
              this.bits = this.data[this.pos++];
              this.colorType = this.data[this.pos++];
              this.compressionMethod = this.data[this.pos++];
              this.filterMethod = this.data[this.pos++];
              this.interlaceMethod = this.data[this.pos++];
              break;
            case 'acTL':
              this.animation = {
                numFrames: this.readUInt32(),
                numPlays: this.readUInt32() || Infinity,
                frames: []
              };
              break;
            case 'PLTE':
              this.palette = this.read(chunkSize);
              break;
            case 'fcTL':
              if (frame) {
                this.animation.frames.push(frame);
              }
              this.pos += 4;
              frame = {
                width: this.readUInt32(),
                height: this.readUInt32(),
                xOffset: this.readUInt32(),
                yOffset: this.readUInt32()
              };
              delayNum = this.readUInt16();
              delayDen = this.readUInt16() || 100;
              frame.delay = 1000 * delayNum / delayDen;
              frame.disposeOp = this.data[this.pos++];
              frame.blendOp = this.data[this.pos++];
              frame.data = [];
              break;
            case 'IDAT':
            case 'fdAT':
              if (section === 'fdAT') {
                this.pos += 4;
                chunkSize -= 4;
              }
              data = (frame != null ? frame.data : void 0) || this.imgData;
              for (i = _i = 0; 0 <= chunkSize ? _i < chunkSize : _i > chunkSize; i = 0 <= chunkSize ? ++_i : --_i) {
                data.push(this.data[this.pos++]);
              }
              break;
            case 'tRNS':
              this.transparency = {};
              switch (this.colorType) {
                case 3:
              	palLen = this.palette.length/3;
                  this.transparency.indexed = this.read(chunkSize);
                  if(this.transparency.indexed.length > palLen)
                  	throw new Error('More transparent colors than palette size');
                  /*
                   * According to the PNG spec trns should be increased to the same size as palette if shorter
                   */
                  //palShort = 255 - this.transparency.indexed.length;
                  palShort = palLen - this.transparency.indexed.length;
                  if (palShort > 0) {
                    for (i = _j = 0; 0 <= palShort ? _j < palShort : _j > palShort; i = 0 <= palShort ? ++_j : --_j) {
                      this.transparency.indexed.push(255);
                    }
                  }
                  break;
                case 0:
                  this.transparency.grayscale = this.read(chunkSize)[0];
                  break;
                case 2:
                  this.transparency.rgb = this.read(chunkSize);
              }
              break;
            case 'tEXt':
              text = this.read(chunkSize);
              index = text.indexOf(0);
              key = String.fromCharCode.apply(String, text.slice(0, index));
              this.text[key] = String.fromCharCode.apply(String, text.slice(index + 1));
              break;
            case 'IEND':
              if (frame) {
                this.animation.frames.push(frame);
              }
              this.colors = (function() {
                switch (this.colorType) {
                  case 0:
                  case 3:
                  case 4:
                    return 1;
                  case 2:
                  case 6:
                    return 3;
                }
              }).call(this);
              this.hasAlphaChannel = (_ref = this.colorType) === 4 || _ref === 6;
              colors = this.colors + (this.hasAlphaChannel ? 1 : 0);
              this.pixelBitlength = this.bits * colors;
              this.colorSpace = (function() {
                switch (this.colors) {
                  case 1:
                    return 'DeviceGray';
                  case 3:
                    return 'DeviceRGB';
                }
              }).call(this);
              this.imgData = new Uint8Array(this.imgData);
              return;
            default:
              this.pos += chunkSize;
          }
          this.pos += 4;
          if (this.pos > this.data.length) {
            throw new Error("Incomplete or corrupt PNG file");
          }
        }
        return;
      }

      PNG.prototype.read = function(bytes) {
        var i, _i, _results;
        _results = [];
        for (i = _i = 0; 0 <= bytes ? _i < bytes : _i > bytes; i = 0 <= bytes ? ++_i : --_i) {
          _results.push(this.data[this.pos++]);
        }
        return _results;
      };

      PNG.prototype.readUInt32 = function() {
        var b1, b2, b3, b4;
        b1 = this.data[this.pos++] << 24;
        b2 = this.data[this.pos++] << 16;
        b3 = this.data[this.pos++] << 8;
        b4 = this.data[this.pos++];
        return b1 | b2 | b3 | b4;
      };

      PNG.prototype.readUInt16 = function() {
        var b1, b2;
        b1 = this.data[this.pos++] << 8;
        b2 = this.data[this.pos++];
        return b1 | b2;
      };

      PNG.prototype.decodePixels = function(data) {
        var abyte, c, col, i, left, length, p, pa, paeth, pb, pc, pixelBytes, pixels, pos, row, scanlineLength, upper, upperLeft, _i, _j, _k, _l, _m;
        if (data == null) {
          data = this.imgData;
        }
        if (data.length === 0) {
          return new Uint8Array(0);
        }
        data = new FlateStream(data);
        data = data.getBytes();
        pixelBytes = this.pixelBitlength / 8;
        scanlineLength = pixelBytes * this.width;
        pixels = new Uint8Array(scanlineLength * this.height);
        length = data.length;
        row = 0;
        pos = 0;
        c = 0;
        while (pos < length) {
          switch (data[pos++]) {
            case 0:
              for (i = _i = 0; _i < scanlineLength; i = _i += 1) {
                pixels[c++] = data[pos++];
              }
              break;
            case 1:
              for (i = _j = 0; _j < scanlineLength; i = _j += 1) {
                abyte = data[pos++];
                left = i < pixelBytes ? 0 : pixels[c - pixelBytes];
                pixels[c++] = (abyte + left) % 256;
              }
              break;
            case 2:
              for (i = _k = 0; _k < scanlineLength; i = _k += 1) {
                abyte = data[pos++];
                col = (i - (i % pixelBytes)) / pixelBytes;
                upper = row && pixels[(row - 1) * scanlineLength + col * pixelBytes + (i % pixelBytes)];
                pixels[c++] = (upper + abyte) % 256;
              }
              break;
            case 3:
              for (i = _l = 0; _l < scanlineLength; i = _l += 1) {
                abyte = data[pos++];
                col = (i - (i % pixelBytes)) / pixelBytes;
                left = i < pixelBytes ? 0 : pixels[c - pixelBytes];
                upper = row && pixels[(row - 1) * scanlineLength + col * pixelBytes + (i % pixelBytes)];
                pixels[c++] = (abyte + Math.floor((left + upper) / 2)) % 256;
              }
              break;
            case 4:
              for (i = _m = 0; _m < scanlineLength; i = _m += 1) {
                abyte = data[pos++];
                col = (i - (i % pixelBytes)) / pixelBytes;
                left = i < pixelBytes ? 0 : pixels[c - pixelBytes];
                if (row === 0) {
                  upper = upperLeft = 0;
                } else {
                  upper = pixels[(row - 1) * scanlineLength + col * pixelBytes + (i % pixelBytes)];
                  upperLeft = col && pixels[(row - 1) * scanlineLength + (col - 1) * pixelBytes + (i % pixelBytes)];
                }
                p = left + upper - upperLeft;
                pa = Math.abs(p - left);
                pb = Math.abs(p - upper);
                pc = Math.abs(p - upperLeft);
                if (pa <= pb && pa <= pc) {
                  paeth = left;
                } else if (pb <= pc) {
                  paeth = upper;
                } else {
                  paeth = upperLeft;
                }
                pixels[c++] = (abyte + paeth) % 256;
              }
              break;
            default:
              throw new Error("Invalid filter algorithm: " + data[pos - 1]);
          }
          row++;
        }
        return pixels;
      };

      PNG.prototype.decodePalette = function() {
        var c, i, length, palette, pos, ret, transparency, _i, _ref, _ref1;
        palette = this.palette;
        transparency = this.transparency.indexed || [];
        ret = new Uint8Array((transparency.length || 0) + palette.length);
        pos = 0;
        length = palette.length;
        c = 0;
        for (i = _i = 0, _ref = palette.length; _i < _ref; i = _i += 3) {
          ret[pos++] = palette[i];
          ret[pos++] = palette[i + 1];
          ret[pos++] = palette[i + 2];
          ret[pos++] = (_ref1 = transparency[c++]) != null ? _ref1 : 255;
        }
        return ret;
      };

      PNG.prototype.copyToImageData = function(imageData, pixels) {
        var alpha, colors, data, i, input, j, k, length, palette, v, _ref;
        colors = this.colors;
        palette = null;
        alpha = this.hasAlphaChannel;
        if (this.palette.length) {
          palette = (_ref = this._decodedPalette) != null ? _ref : this._decodedPalette = this.decodePalette();
          colors = 4;
          alpha = true;
        }
        data = imageData.data || imageData;
        length = data.length;
        input = palette || pixels;
        i = j = 0;
        if (colors === 1) {
          while (i < length) {
            k = palette ? pixels[i / 4] * 4 : j;
            v = input[k++];
            data[i++] = v;
            data[i++] = v;
            data[i++] = v;
            data[i++] = alpha ? input[k++] : 255;
            j = k;
          }
        } else {
          while (i < length) {
            k = palette ? pixels[i / 4] * 4 : j;
            data[i++] = input[k++];
            data[i++] = input[k++];
            data[i++] = input[k++];
            data[i++] = alpha ? input[k++] : 255;
            j = k;
          }
        }
      };

      PNG.prototype.decode = function() {
        var ret;
        ret = new Uint8Array(this.width * this.height * 4);
        this.copyToImageData(ret, this.decodePixels());
        return ret;
      };

      try {
          scratchCanvas = global.document.createElement('canvas');
          scratchCtx = scratchCanvas.getContext('2d');
      } catch(e) {
          return -1;
      }

      makeImage = function(imageData) {
        var img;
        scratchCtx.width = imageData.width;
        scratchCtx.height = imageData.height;
        scratchCtx.clearRect(0, 0, imageData.width, imageData.height);
        scratchCtx.putImageData(imageData, 0, 0);
        img = new Image;
        img.src = scratchCanvas.toDataURL();
        return img;
      };

      PNG.prototype.decodeFrames = function(ctx) {
        var frame, i, imageData, pixels, _i, _len, _ref, _results;
        if (!this.animation) {
          return;
        }
        _ref = this.animation.frames;
        _results = [];
        for (i = _i = 0, _len = _ref.length; _i < _len; i = ++_i) {
          frame = _ref[i];
          imageData = ctx.createImageData(frame.width, frame.height);
          pixels = this.decodePixels(new Uint8Array(frame.data));
          this.copyToImageData(imageData, pixels);
          frame.imageData = imageData;
          _results.push(frame.image = makeImage(imageData));
        }
        return _results;
      };

      PNG.prototype.renderFrame = function(ctx, number) {
        var frame, frames, prev;
        frames = this.animation.frames;
        frame = frames[number];
        prev = frames[number - 1];
        if (number === 0) {
          ctx.clearRect(0, 0, this.width, this.height);
        }
        if ((prev != null ? prev.disposeOp : void 0) === APNG_DISPOSE_OP_BACKGROUND) {
          ctx.clearRect(prev.xOffset, prev.yOffset, prev.width, prev.height);
        } else if ((prev != null ? prev.disposeOp : void 0) === APNG_DISPOSE_OP_PREVIOUS) {
          ctx.putImageData(prev.imageData, prev.xOffset, prev.yOffset);
        }
        if (frame.blendOp === APNG_BLEND_OP_SOURCE) {
          ctx.clearRect(frame.xOffset, frame.yOffset, frame.width, frame.height);
        }
        return ctx.drawImage(frame.image, frame.xOffset, frame.yOffset);
      };

      PNG.prototype.animate = function(ctx) {
        var doFrame, frameNumber, frames, numFrames, numPlays, _ref,
          _this = this;
        frameNumber = 0;
        _ref = this.animation, numFrames = _ref.numFrames, frames = _ref.frames, numPlays = _ref.numPlays;
        return (doFrame = function() {
          var f, frame;
          f = frameNumber++ % numFrames;
          frame = frames[f];
          _this.renderFrame(ctx, f);
          if (numFrames > 1 && frameNumber / numFrames < numPlays) {
            return _this.animation._timeout = setTimeout(doFrame, frame.delay);
          }
        })();
      };

      PNG.prototype.stopAnimation = function() {
        var _ref;
        return clearTimeout((_ref = this.animation) != null ? _ref._timeout : void 0);
      };

      PNG.prototype.render = function(canvas) {
        var ctx, data;
        if (canvas._png) {
          canvas._png.stopAnimation();
        }
        canvas._png = this;
        canvas.width = this.width;
        canvas.height = this.height;
        ctx = canvas.getContext("2d");
        if (this.animation) {
          this.decodeFrames(ctx);
          return this.animate(ctx);
        } else {
          data = ctx.createImageData(this.width, this.height);
          this.copyToImageData(data, this.decodePixels());
          return ctx.putImageData(data, 0, 0);
        }
      };

      return PNG;

    })();

    global.PNG = PNG;

  })(typeof window !== "undefined" && window || undefined);

  /*
   * Extracted from pdf.js
   * https://github.com/andreasgal/pdf.js
   *
   * Copyright (c) 2011 Mozilla Foundation
   *
   * Contributors: Andreas Gal <gal@mozilla.com>
   *               Chris G Jones <cjones@mozilla.com>
   *               Shaon Barman <shaon.barman@gmail.com>
   *               Vivien Nicolas <21@vingtetun.org>
   *               Justin D'Arcangelo <justindarc@gmail.com>
   *               Yury Delendik
   *
   * 
   */

  var DecodeStream = (function() {
    function constructor() {
      this.pos = 0;
      this.bufferLength = 0;
      this.eof = false;
      this.buffer = null;
    }

    constructor.prototype = {
      ensureBuffer: function decodestream_ensureBuffer(requested) {
        var buffer = this.buffer;
        var current = buffer ? buffer.byteLength : 0;
        if (requested < current)
          return buffer;
        var size = 512;
        while (size < requested)
          size <<= 1;
        var buffer2 = new Uint8Array(size);
        for (var i = 0; i < current; ++i)
          buffer2[i] = buffer[i];
        return this.buffer = buffer2;
      },
      getByte: function decodestream_getByte() {
        var pos = this.pos;
        while (this.bufferLength <= pos) {
          if (this.eof)
            return null;
          this.readBlock();
        }
        return this.buffer[this.pos++];
      },
      getBytes: function decodestream_getBytes(length) {
        var pos = this.pos;

        if (length) {
          this.ensureBuffer(pos + length);
          var end = pos + length;

          while (!this.eof && this.bufferLength < end)
            this.readBlock();

          var bufEnd = this.bufferLength;
          if (end > bufEnd)
            end = bufEnd;
        } else {
          while (!this.eof)
            this.readBlock();

          var end = this.bufferLength;
        }

        this.pos = end;
        return this.buffer.subarray(pos, end);
      },
      lookChar: function decodestream_lookChar() {
        var pos = this.pos;
        while (this.bufferLength <= pos) {
          if (this.eof)
            return null;
          this.readBlock();
        }
        return String.fromCharCode(this.buffer[this.pos]);
      },
      getChar: function decodestream_getChar() {
        var pos = this.pos;
        while (this.bufferLength <= pos) {
          if (this.eof)
            return null;
          this.readBlock();
        }
        return String.fromCharCode(this.buffer[this.pos++]);
      },
      makeSubStream: function decodestream_makeSubstream(start, length, dict) {
        var end = start + length;
        while (this.bufferLength <= end && !this.eof)
          this.readBlock();
        return new Stream(this.buffer, start, length, dict);
      },
      skip: function decodestream_skip(n) {
        if (!n)
          n = 1;
        this.pos += n;
      },
      reset: function decodestream_reset() {
        this.pos = 0;
      }
    };

    return constructor;
  })();

  var FlateStream = (function() {
    if (typeof Uint32Array === 'undefined') {
      return undefined;
    }
    var codeLenCodeMap = new Uint32Array([
      16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15
    ]);

    var lengthDecode = new Uint32Array([
      0x00003, 0x00004, 0x00005, 0x00006, 0x00007, 0x00008, 0x00009, 0x0000a,
      0x1000b, 0x1000d, 0x1000f, 0x10011, 0x20013, 0x20017, 0x2001b, 0x2001f,
      0x30023, 0x3002b, 0x30033, 0x3003b, 0x40043, 0x40053, 0x40063, 0x40073,
      0x50083, 0x500a3, 0x500c3, 0x500e3, 0x00102, 0x00102, 0x00102
    ]);

    var distDecode = new Uint32Array([
      0x00001, 0x00002, 0x00003, 0x00004, 0x10005, 0x10007, 0x20009, 0x2000d,
      0x30011, 0x30019, 0x40021, 0x40031, 0x50041, 0x50061, 0x60081, 0x600c1,
      0x70101, 0x70181, 0x80201, 0x80301, 0x90401, 0x90601, 0xa0801, 0xa0c01,
      0xb1001, 0xb1801, 0xc2001, 0xc3001, 0xd4001, 0xd6001
    ]);

    var fixedLitCodeTab = [new Uint32Array([
      0x70100, 0x80050, 0x80010, 0x80118, 0x70110, 0x80070, 0x80030, 0x900c0,
      0x70108, 0x80060, 0x80020, 0x900a0, 0x80000, 0x80080, 0x80040, 0x900e0,
      0x70104, 0x80058, 0x80018, 0x90090, 0x70114, 0x80078, 0x80038, 0x900d0,
      0x7010c, 0x80068, 0x80028, 0x900b0, 0x80008, 0x80088, 0x80048, 0x900f0,
      0x70102, 0x80054, 0x80014, 0x8011c, 0x70112, 0x80074, 0x80034, 0x900c8,
      0x7010a, 0x80064, 0x80024, 0x900a8, 0x80004, 0x80084, 0x80044, 0x900e8,
      0x70106, 0x8005c, 0x8001c, 0x90098, 0x70116, 0x8007c, 0x8003c, 0x900d8,
      0x7010e, 0x8006c, 0x8002c, 0x900b8, 0x8000c, 0x8008c, 0x8004c, 0x900f8,
      0x70101, 0x80052, 0x80012, 0x8011a, 0x70111, 0x80072, 0x80032, 0x900c4,
      0x70109, 0x80062, 0x80022, 0x900a4, 0x80002, 0x80082, 0x80042, 0x900e4,
      0x70105, 0x8005a, 0x8001a, 0x90094, 0x70115, 0x8007a, 0x8003a, 0x900d4,
      0x7010d, 0x8006a, 0x8002a, 0x900b4, 0x8000a, 0x8008a, 0x8004a, 0x900f4,
      0x70103, 0x80056, 0x80016, 0x8011e, 0x70113, 0x80076, 0x80036, 0x900cc,
      0x7010b, 0x80066, 0x80026, 0x900ac, 0x80006, 0x80086, 0x80046, 0x900ec,
      0x70107, 0x8005e, 0x8001e, 0x9009c, 0x70117, 0x8007e, 0x8003e, 0x900dc,
      0x7010f, 0x8006e, 0x8002e, 0x900bc, 0x8000e, 0x8008e, 0x8004e, 0x900fc,
      0x70100, 0x80051, 0x80011, 0x80119, 0x70110, 0x80071, 0x80031, 0x900c2,
      0x70108, 0x80061, 0x80021, 0x900a2, 0x80001, 0x80081, 0x80041, 0x900e2,
      0x70104, 0x80059, 0x80019, 0x90092, 0x70114, 0x80079, 0x80039, 0x900d2,
      0x7010c, 0x80069, 0x80029, 0x900b2, 0x80009, 0x80089, 0x80049, 0x900f2,
      0x70102, 0x80055, 0x80015, 0x8011d, 0x70112, 0x80075, 0x80035, 0x900ca,
      0x7010a, 0x80065, 0x80025, 0x900aa, 0x80005, 0x80085, 0x80045, 0x900ea,
      0x70106, 0x8005d, 0x8001d, 0x9009a, 0x70116, 0x8007d, 0x8003d, 0x900da,
      0x7010e, 0x8006d, 0x8002d, 0x900ba, 0x8000d, 0x8008d, 0x8004d, 0x900fa,
      0x70101, 0x80053, 0x80013, 0x8011b, 0x70111, 0x80073, 0x80033, 0x900c6,
      0x70109, 0x80063, 0x80023, 0x900a6, 0x80003, 0x80083, 0x80043, 0x900e6,
      0x70105, 0x8005b, 0x8001b, 0x90096, 0x70115, 0x8007b, 0x8003b, 0x900d6,
      0x7010d, 0x8006b, 0x8002b, 0x900b6, 0x8000b, 0x8008b, 0x8004b, 0x900f6,
      0x70103, 0x80057, 0x80017, 0x8011f, 0x70113, 0x80077, 0x80037, 0x900ce,
      0x7010b, 0x80067, 0x80027, 0x900ae, 0x80007, 0x80087, 0x80047, 0x900ee,
      0x70107, 0x8005f, 0x8001f, 0x9009e, 0x70117, 0x8007f, 0x8003f, 0x900de,
      0x7010f, 0x8006f, 0x8002f, 0x900be, 0x8000f, 0x8008f, 0x8004f, 0x900fe,
      0x70100, 0x80050, 0x80010, 0x80118, 0x70110, 0x80070, 0x80030, 0x900c1,
      0x70108, 0x80060, 0x80020, 0x900a1, 0x80000, 0x80080, 0x80040, 0x900e1,
      0x70104, 0x80058, 0x80018, 0x90091, 0x70114, 0x80078, 0x80038, 0x900d1,
      0x7010c, 0x80068, 0x80028, 0x900b1, 0x80008, 0x80088, 0x80048, 0x900f1,
      0x70102, 0x80054, 0x80014, 0x8011c, 0x70112, 0x80074, 0x80034, 0x900c9,
      0x7010a, 0x80064, 0x80024, 0x900a9, 0x80004, 0x80084, 0x80044, 0x900e9,
      0x70106, 0x8005c, 0x8001c, 0x90099, 0x70116, 0x8007c, 0x8003c, 0x900d9,
      0x7010e, 0x8006c, 0x8002c, 0x900b9, 0x8000c, 0x8008c, 0x8004c, 0x900f9,
      0x70101, 0x80052, 0x80012, 0x8011a, 0x70111, 0x80072, 0x80032, 0x900c5,
      0x70109, 0x80062, 0x80022, 0x900a5, 0x80002, 0x80082, 0x80042, 0x900e5,
      0x70105, 0x8005a, 0x8001a, 0x90095, 0x70115, 0x8007a, 0x8003a, 0x900d5,
      0x7010d, 0x8006a, 0x8002a, 0x900b5, 0x8000a, 0x8008a, 0x8004a, 0x900f5,
      0x70103, 0x80056, 0x80016, 0x8011e, 0x70113, 0x80076, 0x80036, 0x900cd,
      0x7010b, 0x80066, 0x80026, 0x900ad, 0x80006, 0x80086, 0x80046, 0x900ed,
      0x70107, 0x8005e, 0x8001e, 0x9009d, 0x70117, 0x8007e, 0x8003e, 0x900dd,
      0x7010f, 0x8006e, 0x8002e, 0x900bd, 0x8000e, 0x8008e, 0x8004e, 0x900fd,
      0x70100, 0x80051, 0x80011, 0x80119, 0x70110, 0x80071, 0x80031, 0x900c3,
      0x70108, 0x80061, 0x80021, 0x900a3, 0x80001, 0x80081, 0x80041, 0x900e3,
      0x70104, 0x80059, 0x80019, 0x90093, 0x70114, 0x80079, 0x80039, 0x900d3,
      0x7010c, 0x80069, 0x80029, 0x900b3, 0x80009, 0x80089, 0x80049, 0x900f3,
      0x70102, 0x80055, 0x80015, 0x8011d, 0x70112, 0x80075, 0x80035, 0x900cb,
      0x7010a, 0x80065, 0x80025, 0x900ab, 0x80005, 0x80085, 0x80045, 0x900eb,
      0x70106, 0x8005d, 0x8001d, 0x9009b, 0x70116, 0x8007d, 0x8003d, 0x900db,
      0x7010e, 0x8006d, 0x8002d, 0x900bb, 0x8000d, 0x8008d, 0x8004d, 0x900fb,
      0x70101, 0x80053, 0x80013, 0x8011b, 0x70111, 0x80073, 0x80033, 0x900c7,
      0x70109, 0x80063, 0x80023, 0x900a7, 0x80003, 0x80083, 0x80043, 0x900e7,
      0x70105, 0x8005b, 0x8001b, 0x90097, 0x70115, 0x8007b, 0x8003b, 0x900d7,
      0x7010d, 0x8006b, 0x8002b, 0x900b7, 0x8000b, 0x8008b, 0x8004b, 0x900f7,
      0x70103, 0x80057, 0x80017, 0x8011f, 0x70113, 0x80077, 0x80037, 0x900cf,
      0x7010b, 0x80067, 0x80027, 0x900af, 0x80007, 0x80087, 0x80047, 0x900ef,
      0x70107, 0x8005f, 0x8001f, 0x9009f, 0x70117, 0x8007f, 0x8003f, 0x900df,
      0x7010f, 0x8006f, 0x8002f, 0x900bf, 0x8000f, 0x8008f, 0x8004f, 0x900ff
    ]), 9];

    var fixedDistCodeTab = [new Uint32Array([
      0x50000, 0x50010, 0x50008, 0x50018, 0x50004, 0x50014, 0x5000c, 0x5001c,
      0x50002, 0x50012, 0x5000a, 0x5001a, 0x50006, 0x50016, 0x5000e, 0x00000,
      0x50001, 0x50011, 0x50009, 0x50019, 0x50005, 0x50015, 0x5000d, 0x5001d,
      0x50003, 0x50013, 0x5000b, 0x5001b, 0x50007, 0x50017, 0x5000f, 0x00000
    ]), 5];
    
    function error(e) {
        throw new Error(e)
    }

    function constructor(bytes) {
      //var bytes = stream.getBytes();
      var bytesPos = 0;

      var cmf = bytes[bytesPos++];
      var flg = bytes[bytesPos++];
      if (cmf == -1 || flg == -1)
        error('Invalid header in flate stream');
      if ((cmf & 0x0f) != 0x08)
        error('Unknown compression method in flate stream');
      if ((((cmf << 8) + flg) % 31) != 0)
        error('Bad FCHECK in flate stream');
      if (flg & 0x20)
        error('FDICT bit set in flate stream');

      this.bytes = bytes;
      this.bytesPos = bytesPos;

      this.codeSize = 0;
      this.codeBuf = 0;

      DecodeStream.call(this);
    }

    constructor.prototype = Object.create(DecodeStream.prototype);

    constructor.prototype.getBits = function(bits) {
      var codeSize = this.codeSize;
      var codeBuf = this.codeBuf;
      var bytes = this.bytes;
      var bytesPos = this.bytesPos;

      var b;
      while (codeSize < bits) {
        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad encoding in flate stream');
        codeBuf |= b << codeSize;
        codeSize += 8;
      }
      b = codeBuf & ((1 << bits) - 1);
      this.codeBuf = codeBuf >> bits;
      this.codeSize = codeSize -= bits;
      this.bytesPos = bytesPos;
      return b;
    };

    constructor.prototype.getCode = function(table) {
      var codes = table[0];
      var maxLen = table[1];
      var codeSize = this.codeSize;
      var codeBuf = this.codeBuf;
      var bytes = this.bytes;
      var bytesPos = this.bytesPos;

      while (codeSize < maxLen) {
        var b;
        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad encoding in flate stream');
        codeBuf |= (b << codeSize);
        codeSize += 8;
      }
      var code = codes[codeBuf & ((1 << maxLen) - 1)];
      var codeLen = code >> 16;
      var codeVal = code & 0xffff;
      if (codeSize == 0 || codeSize < codeLen || codeLen == 0)
        error('Bad encoding in flate stream');
      this.codeBuf = (codeBuf >> codeLen);
      this.codeSize = (codeSize - codeLen);
      this.bytesPos = bytesPos;
      return codeVal;
    };

    constructor.prototype.generateHuffmanTable = function(lengths) {
      var n = lengths.length;

      // find max code length
      var maxLen = 0;
      for (var i = 0; i < n; ++i) {
        if (lengths[i] > maxLen)
          maxLen = lengths[i];
      }

      // build the table
      var size = 1 << maxLen;
      var codes = new Uint32Array(size);
      for (var len = 1, code = 0, skip = 2;
           len <= maxLen;
           ++len, code <<= 1, skip <<= 1) {
        for (var val = 0; val < n; ++val) {
          if (lengths[val] == len) {
            // bit-reverse the code
            var code2 = 0;
            var t = code;
            for (var i = 0; i < len; ++i) {
              code2 = (code2 << 1) | (t & 1);
              t >>= 1;
            }

            // fill the table entries
            for (var i = code2; i < size; i += skip)
              codes[i] = (len << 16) | val;

            ++code;
          }
        }
      }

      return [codes, maxLen];
    };

    constructor.prototype.readBlock = function() {
      function repeat(stream, array, len, offset, what) {
        var repeat = stream.getBits(len) + offset;
        while (repeat-- > 0)
          array[i++] = what;
      }

      // read block header
      var hdr = this.getBits(3);
      if (hdr & 1)
        this.eof = true;
      hdr >>= 1;

      if (hdr == 0) { // uncompressed block
        var bytes = this.bytes;
        var bytesPos = this.bytesPos;
        var b;

        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad block header in flate stream');
        var blockLen = b;
        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad block header in flate stream');
        blockLen |= (b << 8);
        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad block header in flate stream');
        var check = b;
        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad block header in flate stream');
        check |= (b << 8);
        if (check != (~blockLen & 0xffff))
          error('Bad uncompressed block length in flate stream');

        this.codeBuf = 0;
        this.codeSize = 0;

        var bufferLength = this.bufferLength;
        var buffer = this.ensureBuffer(bufferLength + blockLen);
        var end = bufferLength + blockLen;
        this.bufferLength = end;
        for (var n = bufferLength; n < end; ++n) {
          if (typeof (b = bytes[bytesPos++]) == 'undefined') {
            this.eof = true;
            break;
          }
          buffer[n] = b;
        }
        this.bytesPos = bytesPos;
        return;
      }

      var litCodeTable;
      var distCodeTable;
      if (hdr == 1) { // compressed block, fixed codes
        litCodeTable = fixedLitCodeTab;
        distCodeTable = fixedDistCodeTab;
      } else if (hdr == 2) { // compressed block, dynamic codes
        var numLitCodes = this.getBits(5) + 257;
        var numDistCodes = this.getBits(5) + 1;
        var numCodeLenCodes = this.getBits(4) + 4;

        // build the code lengths code table
        var codeLenCodeLengths = Array(codeLenCodeMap.length);
        var i = 0;
        while (i < numCodeLenCodes)
          codeLenCodeLengths[codeLenCodeMap[i++]] = this.getBits(3);
        var codeLenCodeTab = this.generateHuffmanTable(codeLenCodeLengths);

        // build the literal and distance code tables
        var len = 0;
        var i = 0;
        var codes = numLitCodes + numDistCodes;
        var codeLengths = new Array(codes);
        while (i < codes) {
          var code = this.getCode(codeLenCodeTab);
          if (code == 16) {
            repeat(this, codeLengths, 2, 3, len);
          } else if (code == 17) {
            repeat(this, codeLengths, 3, 3, len = 0);
          } else if (code == 18) {
            repeat(this, codeLengths, 7, 11, len = 0);
          } else {
            codeLengths[i++] = len = code;
          }
        }

        litCodeTable =
          this.generateHuffmanTable(codeLengths.slice(0, numLitCodes));
        distCodeTable =
          this.generateHuffmanTable(codeLengths.slice(numLitCodes, codes));
      } else {
        error('Unknown block type in flate stream');
      }

      var buffer = this.buffer;
      var limit = buffer ? buffer.length : 0;
      var pos = this.bufferLength;
      while (true) {
        var code1 = this.getCode(litCodeTable);
        if (code1 < 256) {
          if (pos + 1 >= limit) {
            buffer = this.ensureBuffer(pos + 1);
            limit = buffer.length;
          }
          buffer[pos++] = code1;
          continue;
        }
        if (code1 == 256) {
          this.bufferLength = pos;
          return;
        }
        code1 -= 257;
        code1 = lengthDecode[code1];
        var code2 = code1 >> 16;
        if (code2 > 0)
          code2 = this.getBits(code2);
        var len = (code1 & 0xffff) + code2;
        code1 = this.getCode(distCodeTable);
        code1 = distDecode[code1];
        code2 = code1 >> 16;
        if (code2 > 0)
          code2 = this.getBits(code2);
        var dist = (code1 & 0xffff) + code2;
        if (pos + len >= limit) {
          buffer = this.ensureBuffer(pos + len);
          limit = buffer.length;
        }
        for (var k = 0; k < len; ++k, ++pos)
          buffer[pos] = buffer[pos - dist];
      }
    };

    return constructor;
  })();

  /**
   * JavaScript Polyfill functions for jsPDF
   * Collected from public resources by
   * https://github.com/diegocr
   */

  (function (global) {
  	var b64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

  	if (typeof global.btoa === 'undefined') {
  		global.btoa = function(data) {
  			//  discuss at: http://phpjs.org/functions/base64_encode/
  			// original by: Tyler Akins (http://rumkin.com)
  			// improved by: Bayron Guevara
  			// improved by: Thunder.m
  			// improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  			// improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  			// improved by: Rafal Kukawski (http://kukawski.pl)
  			// bugfixed by: Pellentesque Malesuada
  			//   example 1: base64_encode('Kevin van Zonneveld');
  			//   returns 1: 'S2V2aW4gdmFuIFpvbm5ldmVsZA=='

  			var o1,o2,o3,h1,h2,h3,h4,bits,i = 0,ac = 0,enc = '',tmp_arr = [];

  			if (!data) {
  				return data;
  			}

  			do { // pack three octets into four hexets
  				o1 = data.charCodeAt(i++);
  				o2 = data.charCodeAt(i++);
  				o3 = data.charCodeAt(i++);

  				bits = o1 << 16 | o2 << 8 | o3;

  				h1 = bits >> 18 & 0x3f;
  				h2 = bits >> 12 & 0x3f;
  				h3 = bits >> 6 & 0x3f;
  				h4 = bits & 0x3f;

  				// use hexets to index into b64, and append result to encoded string
  				tmp_arr[ac++] = b64.charAt(h1) + b64.charAt(h2) + b64.charAt(h3) + b64.charAt(h4);
  			} while (i < data.length);

  			enc = tmp_arr.join('');

  			var r = data.length % 3;

  			return (r ? enc.slice(0, r - 3) : enc) + '==='.slice(r || 3);
  		};
  	}

  	if (typeof global.atob === 'undefined') {
  		global.atob = function(data) {
  			//  discuss at: http://phpjs.org/functions/base64_decode/
  			// original by: Tyler Akins (http://rumkin.com)
  			// improved by: Thunder.m
  			// improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  			// improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  			//    input by: Aman Gupta
  			//    input by: Brett Zamir (http://brett-zamir.me)
  			// bugfixed by: Onno Marsman
  			// bugfixed by: Pellentesque Malesuada
  			// bugfixed by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  			//   example 1: base64_decode('S2V2aW4gdmFuIFpvbm5ldmVsZA==');
  			//   returns 1: 'Kevin van Zonneveld'

  			var o1,o2,o3,h1,h2,h3,h4,bits,i = 0,ac = 0,dec = '',tmp_arr = [];

  			if (!data) {
  				return data;
  			}

  			data += '';

  			do { // unpack four hexets into three octets using index points in b64
  				h1 = b64.indexOf(data.charAt(i++));
  				h2 = b64.indexOf(data.charAt(i++));
  				h3 = b64.indexOf(data.charAt(i++));
  				h4 = b64.indexOf(data.charAt(i++));

  				bits = h1 << 18 | h2 << 12 | h3 << 6 | h4;

  				o1 = bits >> 16 & 0xff;
  				o2 = bits >> 8 & 0xff;
  				o3 = bits & 0xff;

  				if (h3 == 64) {
  					tmp_arr[ac++] = String.fromCharCode(o1);
  				} else if (h4 == 64) {
  					tmp_arr[ac++] = String.fromCharCode(o1, o2);
  				} else {
  					tmp_arr[ac++] = String.fromCharCode(o1, o2, o3);
  				}
  			} while (i < data.length);

  			dec = tmp_arr.join('');

  			return dec;
  		};
  	}

  	if (!Array.prototype.map) {
  		Array.prototype.map = function(fun /*, thisArg */) {
  			if (this === void 0 || this === null || typeof fun !== "function")
  				throw new TypeError();

  			var t = Object(this), len = t.length >>> 0, res = new Array(len);
  			var thisArg = arguments.length > 1 ? arguments[1] : void 0;
  			for (var i = 0; i < len; i++) {
  				// NOTE: Absolute correctness would demand Object.defineProperty
  				//       be used.  But this method is fairly new, and failure is
  				//       possible only if Object.prototype or Array.prototype
  				//       has a property |i| (very unlikely), so use a less-correct
  				//       but more portable alternative.
  				if (i in t)
  					res[i] = fun.call(thisArg, t[i], i, t);
  			}

  			return res;
  		};
  	}


  	if(!Array.isArray) {
  		Array.isArray = function(arg) {
  			return Object.prototype.toString.call(arg) === '[object Array]';
  		};
  	}

  	if (!Array.prototype.forEach) {
  		Array.prototype.forEach = function(fun, thisArg) {
  			"use strict";

  			if (this === void 0 || this === null || typeof fun !== "function")
  				throw new TypeError();

  			var t = Object(this), len = t.length >>> 0;
  			for (var i = 0; i < len; i++) {
  				if (i in t)
  					fun.call(thisArg, t[i], i, t);
  			}
  		};
  	}

  	if (!Object.keys) {
  		Object.keys = (function () {
  			'use strict';

  			var hasOwnProperty = Object.prototype.hasOwnProperty,
  				hasDontEnumBug = !({toString: null}).propertyIsEnumerable('toString'),
  				dontEnums = ['toString','toLocaleString','valueOf','hasOwnProperty',
  					'isPrototypeOf','propertyIsEnumerable','constructor'],
  				dontEnumsLength = dontEnums.length;

  			return function (obj) {
  				if (typeof obj !== 'object' && (typeof obj !== 'function' || obj === null)) {
  					throw new TypeError();
  				}
  				var result = [], prop, i;

  				for (prop in obj) {
  					if (hasOwnProperty.call(obj, prop)) {
  						result.push(prop);
  					}
  				}

  				if (hasDontEnumBug) {
  					for (i = 0; i < dontEnumsLength; i++) {
  						if (hasOwnProperty.call(obj, dontEnums[i])) {
  							result.push(dontEnums[i]);
  						}
  					}
  				}
  				return result;
  			};
  		}());
  	}

  	if (!String.prototype.trim) {
  		String.prototype.trim = function () {
  			return this.replace(/^\s+|\s+$/g, '');
  		};
  	}
  	if (!String.prototype.trimLeft) {
  		String.prototype.trimLeft = function() {
  			return this.replace(/^\s+/g, "");
  		};
  	}
  	if (!String.prototype.trimRight) {
  		String.prototype.trimRight = function() {
  			return this.replace(/\s+$/g, "");
  		};
  	}

  })(typeof self !== "undefined" && self || typeof window !== "undefined" && window || undefined);

  var jsPDF = jsPDF;

  return jsPDF;

}));
/**
 * @license
 * lodash <https://lodash.com/>
 * Copyright jQuery Foundation and other contributors <https://jquery.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */
;(function() {

  /** Used as a safe reference for `undefined` in pre-ES5 environments. */
  var undefined;

  /** Used as the semantic version number. */
  var VERSION = '4.12.0';

  /** Used as the size to enable large array optimizations. */
  var LARGE_ARRAY_SIZE = 200;

  /** Used as the `TypeError` message for "Functions" methods. */
  var FUNC_ERROR_TEXT = 'Expected a function';

  /** Used to stand-in for `undefined` hash values. */
  var HASH_UNDEFINED = '__lodash_hash_undefined__';

  /** Used as the internal argument placeholder. */
  var PLACEHOLDER = '__lodash_placeholder__';

  /** Used to compose bitmasks for wrapper metadata. */
  var BIND_FLAG = 1,
      BIND_KEY_FLAG = 2,
      CURRY_BOUND_FLAG = 4,
      CURRY_FLAG = 8,
      CURRY_RIGHT_FLAG = 16,
      PARTIAL_FLAG = 32,
      PARTIAL_RIGHT_FLAG = 64,
      ARY_FLAG = 128,
      REARG_FLAG = 256,
      FLIP_FLAG = 512;

  /** Used to compose bitmasks for comparison styles. */
  var UNORDERED_COMPARE_FLAG = 1,
      PARTIAL_COMPARE_FLAG = 2;

  /** Used as default options for `_.truncate`. */
  var DEFAULT_TRUNC_LENGTH = 30,
      DEFAULT_TRUNC_OMISSION = '...';

  /** Used to detect hot functions by number of calls within a span of milliseconds. */
  var HOT_COUNT = 150,
      HOT_SPAN = 16;

  /** Used to indicate the type of lazy iteratees. */
  var LAZY_FILTER_FLAG = 1,
      LAZY_MAP_FLAG = 2,
      LAZY_WHILE_FLAG = 3;

  /** Used as references for various `Number` constants. */
  var INFINITY = 1 / 0,
      MAX_SAFE_INTEGER = 9007199254740991,
      MAX_INTEGER = 1.7976931348623157e+308,
      NAN = 0 / 0;

  /** Used as references for the maximum length and index of an array. */
  var MAX_ARRAY_LENGTH = 4294967295,
      MAX_ARRAY_INDEX = MAX_ARRAY_LENGTH - 1,
      HALF_MAX_ARRAY_LENGTH = MAX_ARRAY_LENGTH >>> 1;

  /** `Object#toString` result references. */
  var argsTag = '[object Arguments]',
      arrayTag = '[object Array]',
      boolTag = '[object Boolean]',
      dateTag = '[object Date]',
      errorTag = '[object Error]',
      funcTag = '[object Function]',
      genTag = '[object GeneratorFunction]',
      mapTag = '[object Map]',
      numberTag = '[object Number]',
      objectTag = '[object Object]',
      promiseTag = '[object Promise]',
      regexpTag = '[object RegExp]',
      setTag = '[object Set]',
      stringTag = '[object String]',
      symbolTag = '[object Symbol]',
      weakMapTag = '[object WeakMap]',
      weakSetTag = '[object WeakSet]';

  var arrayBufferTag = '[object ArrayBuffer]',
      dataViewTag = '[object DataView]',
      float32Tag = '[object Float32Array]',
      float64Tag = '[object Float64Array]',
      int8Tag = '[object Int8Array]',
      int16Tag = '[object Int16Array]',
      int32Tag = '[object Int32Array]',
      uint8Tag = '[object Uint8Array]',
      uint8ClampedTag = '[object Uint8ClampedArray]',
      uint16Tag = '[object Uint16Array]',
      uint32Tag = '[object Uint32Array]';

  /** Used to match empty string literals in compiled template source. */
  var reEmptyStringLeading = /\b__p \+= '';/g,
      reEmptyStringMiddle = /\b(__p \+=) '' \+/g,
      reEmptyStringTrailing = /(__e\(.*?\)|\b__t\)) \+\n'';/g;

  /** Used to match HTML entities and HTML characters. */
  var reEscapedHtml = /&(?:amp|lt|gt|quot|#39|#96);/g,
      reUnescapedHtml = /[&<>"'`]/g,
      reHasEscapedHtml = RegExp(reEscapedHtml.source),
      reHasUnescapedHtml = RegExp(reUnescapedHtml.source);

  /** Used to match template delimiters. */
  var reEscape = /<%-([\s\S]+?)%>/g,
      reEvaluate = /<%([\s\S]+?)%>/g,
      reInterpolate = /<%=([\s\S]+?)%>/g;

  /** Used to match property names within property paths. */
  var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
      reIsPlainProp = /^\w*$/,
      rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]/g;

  /**
   * Used to match `RegExp`
   * [syntax characters](http://ecma-international.org/ecma-262/6.0/#sec-patterns).
   */
  var reRegExpChar = /[\\^$.*+?()[\]{}|]/g,
      reHasRegExpChar = RegExp(reRegExpChar.source);

  /** Used to match leading and trailing whitespace. */
  var reTrim = /^\s+|\s+$/g,
      reTrimStart = /^\s+/,
      reTrimEnd = /\s+$/;

  /** Used to match non-compound words composed of alphanumeric characters. */
  var reBasicWord = /[a-zA-Z0-9]+/g;

  /** Used to match backslashes in property paths. */
  var reEscapeChar = /\\(\\)?/g;

  /**
   * Used to match
   * [ES template delimiters](http://ecma-international.org/ecma-262/6.0/#sec-template-literal-lexical-components).
   */
  var reEsTemplate = /\$\{([^\\}]*(?:\\.[^\\}]*)*)\}/g;

  /** Used to match `RegExp` flags from their coerced string values. */
  var reFlags = /\w*$/;

  /** Used to detect hexadecimal string values. */
  var reHasHexPrefix = /^0x/i;

  /** Used to detect bad signed hexadecimal string values. */
  var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

  /** Used to detect binary string values. */
  var reIsBinary = /^0b[01]+$/i;

  /** Used to detect host constructors (Safari). */
  var reIsHostCtor = /^\[object .+?Constructor\]$/;

  /** Used to detect octal string values. */
  var reIsOctal = /^0o[0-7]+$/i;

  /** Used to detect unsigned integer values. */
  var reIsUint = /^(?:0|[1-9]\d*)$/;

  /** Used to match latin-1 supplementary letters (excluding mathematical operators). */
  var reLatin1 = /[\xc0-\xd6\xd8-\xde\xdf-\xf6\xf8-\xff]/g;

  /** Used to ensure capturing order of template delimiters. */
  var reNoMatch = /($^)/;

  /** Used to match unescaped characters in compiled string literals. */
  var reUnescapedString = /['\n\r\u2028\u2029\\]/g;

  /** Used to compose unicode character classes. */
  var rsAstralRange = '\\ud800-\\udfff',
      rsComboMarksRange = '\\u0300-\\u036f\\ufe20-\\ufe23',
      rsComboSymbolsRange = '\\u20d0-\\u20f0',
      rsDingbatRange = '\\u2700-\\u27bf',
      rsLowerRange = 'a-z\\xdf-\\xf6\\xf8-\\xff',
      rsMathOpRange = '\\xac\\xb1\\xd7\\xf7',
      rsNonCharRange = '\\x00-\\x2f\\x3a-\\x40\\x5b-\\x60\\x7b-\\xbf',
      rsPunctuationRange = '\\u2000-\\u206f',
      rsSpaceRange = ' \\t\\x0b\\f\\xa0\\ufeff\\n\\r\\u2028\\u2029\\u1680\\u180e\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200a\\u202f\\u205f\\u3000',
      rsUpperRange = 'A-Z\\xc0-\\xd6\\xd8-\\xde',
      rsVarRange = '\\ufe0e\\ufe0f',
      rsBreakRange = rsMathOpRange + rsNonCharRange + rsPunctuationRange + rsSpaceRange;

  /** Used to compose unicode capture groups. */
  var rsApos = "['\u2019]",
      rsAstral = '[' + rsAstralRange + ']',
      rsBreak = '[' + rsBreakRange + ']',
      rsCombo = '[' + rsComboMarksRange + rsComboSymbolsRange + ']',
      rsDigits = '\\d+',
      rsDingbat = '[' + rsDingbatRange + ']',
      rsLower = '[' + rsLowerRange + ']',
      rsMisc = '[^' + rsAstralRange + rsBreakRange + rsDigits + rsDingbatRange + rsLowerRange + rsUpperRange + ']',
      rsFitz = '\\ud83c[\\udffb-\\udfff]',
      rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
      rsNonAstral = '[^' + rsAstralRange + ']',
      rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
      rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
      rsUpper = '[' + rsUpperRange + ']',
      rsZWJ = '\\u200d';

  /** Used to compose unicode regexes. */
  var rsLowerMisc = '(?:' + rsLower + '|' + rsMisc + ')',
      rsUpperMisc = '(?:' + rsUpper + '|' + rsMisc + ')',
      rsOptLowerContr = '(?:' + rsApos + '(?:d|ll|m|re|s|t|ve))?',
      rsOptUpperContr = '(?:' + rsApos + '(?:D|LL|M|RE|S|T|VE))?',
      reOptMod = rsModifier + '?',
      rsOptVar = '[' + rsVarRange + ']?',
      rsOptJoin = '(?:' + rsZWJ + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
      rsSeq = rsOptVar + reOptMod + rsOptJoin,
      rsEmoji = '(?:' + [rsDingbat, rsRegional, rsSurrPair].join('|') + ')' + rsSeq,
      rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';

  /** Used to match apostrophes. */
  var reApos = RegExp(rsApos, 'g');

  /**
   * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
   * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
   */
  var reComboMark = RegExp(rsCombo, 'g');

  /** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
  var reComplexSymbol = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');

  /** Used to match complex or compound words. */
  var reComplexWord = RegExp([
    rsUpper + '?' + rsLower + '+' + rsOptLowerContr + '(?=' + [rsBreak, rsUpper, '$'].join('|') + ')',
    rsUpperMisc + '+' + rsOptUpperContr + '(?=' + [rsBreak, rsUpper + rsLowerMisc, '$'].join('|') + ')',
    rsUpper + '?' + rsLowerMisc + '+' + rsOptLowerContr,
    rsUpper + '+' + rsOptUpperContr,
    rsDigits,
    rsEmoji
  ].join('|'), 'g');

  /** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */
  var reHasComplexSymbol = RegExp('[' + rsZWJ + rsAstralRange  + rsComboMarksRange + rsComboSymbolsRange + rsVarRange + ']');

  /** Used to detect strings that need a more robust regexp to match words. */
  var reHasComplexWord = /[a-z][A-Z]|[A-Z]{2,}[a-z]|[0-9][a-zA-Z]|[a-zA-Z][0-9]|[^a-zA-Z0-9 ]/;

  /** Used to assign default `context` object properties. */
  var contextProps = [
    'Array', 'Buffer', 'DataView', 'Date', 'Error', 'Float32Array', 'Float64Array',
    'Function', 'Int8Array', 'Int16Array', 'Int32Array', 'Map', 'Math', 'Object',
    'Promise', 'Reflect', 'RegExp', 'Set', 'String', 'Symbol', 'TypeError',
    'Uint8Array', 'Uint8ClampedArray', 'Uint16Array', 'Uint32Array', 'WeakMap',
    '_', 'clearTimeout', 'isFinite', 'parseInt', 'setTimeout'
  ];

  /** Used to make template sourceURLs easier to identify. */
  var templateCounter = -1;

  /** Used to identify `toStringTag` values of typed arrays. */
  var typedArrayTags = {};
  typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
  typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
  typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
  typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
  typedArrayTags[uint32Tag] = true;
  typedArrayTags[argsTag] = typedArrayTags[arrayTag] =
  typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
  typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
  typedArrayTags[errorTag] = typedArrayTags[funcTag] =
  typedArrayTags[mapTag] = typedArrayTags[numberTag] =
  typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
  typedArrayTags[setTag] = typedArrayTags[stringTag] =
  typedArrayTags[weakMapTag] = false;

  /** Used to identify `toStringTag` values supported by `_.clone`. */
  var cloneableTags = {};
  cloneableTags[argsTag] = cloneableTags[arrayTag] =
  cloneableTags[arrayBufferTag] = cloneableTags[dataViewTag] =
  cloneableTags[boolTag] = cloneableTags[dateTag] =
  cloneableTags[float32Tag] = cloneableTags[float64Tag] =
  cloneableTags[int8Tag] = cloneableTags[int16Tag] =
  cloneableTags[int32Tag] = cloneableTags[mapTag] =
  cloneableTags[numberTag] = cloneableTags[objectTag] =
  cloneableTags[regexpTag] = cloneableTags[setTag] =
  cloneableTags[stringTag] = cloneableTags[symbolTag] =
  cloneableTags[uint8Tag] = cloneableTags[uint8ClampedTag] =
  cloneableTags[uint16Tag] = cloneableTags[uint32Tag] = true;
  cloneableTags[errorTag] = cloneableTags[funcTag] =
  cloneableTags[weakMapTag] = false;

  /** Used to map latin-1 supplementary letters to basic latin letters. */
  var deburredLetters = {
    '\xc0': 'A',  '\xc1': 'A', '\xc2': 'A', '\xc3': 'A', '\xc4': 'A', '\xc5': 'A',
    '\xe0': 'a',  '\xe1': 'a', '\xe2': 'a', '\xe3': 'a', '\xe4': 'a', '\xe5': 'a',
    '\xc7': 'C',  '\xe7': 'c',
    '\xd0': 'D',  '\xf0': 'd',
    '\xc8': 'E',  '\xc9': 'E', '\xca': 'E', '\xcb': 'E',
    '\xe8': 'e',  '\xe9': 'e', '\xea': 'e', '\xeb': 'e',
    '\xcC': 'I',  '\xcd': 'I', '\xce': 'I', '\xcf': 'I',
    '\xeC': 'i',  '\xed': 'i', '\xee': 'i', '\xef': 'i',
    '\xd1': 'N',  '\xf1': 'n',
    '\xd2': 'O',  '\xd3': 'O', '\xd4': 'O', '\xd5': 'O', '\xd6': 'O', '\xd8': 'O',
    '\xf2': 'o',  '\xf3': 'o', '\xf4': 'o', '\xf5': 'o', '\xf6': 'o', '\xf8': 'o',
    '\xd9': 'U',  '\xda': 'U', '\xdb': 'U', '\xdc': 'U',
    '\xf9': 'u',  '\xfa': 'u', '\xfb': 'u', '\xfc': 'u',
    '\xdd': 'Y',  '\xfd': 'y', '\xff': 'y',
    '\xc6': 'Ae', '\xe6': 'ae',
    '\xde': 'Th', '\xfe': 'th',
    '\xdf': 'ss'
  };

  /** Used to map characters to HTML entities. */
  var htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '`': '&#96;'
  };

  /** Used to map HTML entities to characters. */
  var htmlUnescapes = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&#96;': '`'
  };

  /** Used to determine if values are of the language type `Object`. */
  var objectTypes = {
    'function': true,
    'object': true
  };

  /** Used to escape characters for inclusion in compiled string literals. */
  var stringEscapes = {
    '\\': '\\',
    "'": "'",
    '\n': 'n',
    '\r': 'r',
    '\u2028': 'u2028',
    '\u2029': 'u2029'
  };

  /** Built-in method references without a dependency on `root`. */
  var freeParseFloat = parseFloat,
      freeParseInt = parseInt;

  /** Detect free variable `exports`. */
  var freeExports = (objectTypes[typeof exports] && exports && !exports.nodeType)
    ? exports
    : undefined;

  /** Detect free variable `module`. */
  var freeModule = (objectTypes[typeof module] && module && !module.nodeType)
    ? module
    : undefined;

  /** Detect the popular CommonJS extension `module.exports`. */
  var moduleExports = (freeModule && freeModule.exports === freeExports)
    ? freeExports
    : undefined;

  /** Detect free variable `global` from Node.js. */
  var freeGlobal = checkGlobal(freeExports && freeModule && typeof global == 'object' && global);

  /** Detect free variable `self`. */
  var freeSelf = checkGlobal(objectTypes[typeof self] && self);

  /** Detect free variable `window`. */
  var freeWindow = checkGlobal(objectTypes[typeof window] && window);

  /** Detect `this` as the global object. */
  var thisGlobal = checkGlobal(objectTypes[typeof this] && this);

  /**
   * Used as a reference to the global object.
   *
   * The `this` value is used if it's the global object to avoid Greasemonkey's
   * restricted `window` object, otherwise the `window` object is used.
   */
  var root = freeGlobal ||
    ((freeWindow !== (thisGlobal && thisGlobal.window)) && freeWindow) ||
      freeSelf || thisGlobal || Function('return this')();

  /*--------------------------------------------------------------------------*/

  /**
   * Adds the key-value `pair` to `map`.
   *
   * @private
   * @param {Object} map The map to modify.
   * @param {Array} pair The key-value pair to add.
   * @returns {Object} Returns `map`.
   */
  function addMapEntry(map, pair) {
    // Don't return `Map#set` because it doesn't return the map instance in IE 11.
    map.set(pair[0], pair[1]);
    return map;
  }

  /**
   * Adds `value` to `set`.
   *
   * @private
   * @param {Object} set The set to modify.
   * @param {*} value The value to add.
   * @returns {Object} Returns `set`.
   */
  function addSetEntry(set, value) {
    set.add(value);
    return set;
  }

  /**
   * A faster alternative to `Function#apply`, this function invokes `func`
   * with the `this` binding of `thisArg` and the arguments of `args`.
   *
   * @private
   * @param {Function} func The function to invoke.
   * @param {*} thisArg The `this` binding of `func`.
   * @param {Array} args The arguments to invoke `func` with.
   * @returns {*} Returns the result of `func`.
   */
  function apply(func, thisArg, args) {
    var length = args.length;
    switch (length) {
      case 0: return func.call(thisArg);
      case 1: return func.call(thisArg, args[0]);
      case 2: return func.call(thisArg, args[0], args[1]);
      case 3: return func.call(thisArg, args[0], args[1], args[2]);
    }
    return func.apply(thisArg, args);
  }

  /**
   * A specialized version of `baseAggregator` for arrays.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} setter The function to set `accumulator` values.
   * @param {Function} iteratee The iteratee to transform keys.
   * @param {Object} accumulator The initial aggregated object.
   * @returns {Function} Returns `accumulator`.
   */
  function arrayAggregator(array, setter, iteratee, accumulator) {
    var index = -1,
        length = array.length;

    while (++index < length) {
      var value = array[index];
      setter(accumulator, value, iteratee(value), array);
    }
    return accumulator;
  }

  /**
   * A specialized version of `_.forEach` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns `array`.
   */
  function arrayEach(array, iteratee) {
    var index = -1,
        length = array.length;

    while (++index < length) {
      if (iteratee(array[index], index, array) === false) {
        break;
      }
    }
    return array;
  }

  /**
   * A specialized version of `_.forEachRight` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns `array`.
   */
  function arrayEachRight(array, iteratee) {
    var length = array.length;

    while (length--) {
      if (iteratee(array[length], length, array) === false) {
        break;
      }
    }
    return array;
  }

  /**
   * A specialized version of `_.every` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} predicate The function invoked per iteration.
   * @returns {boolean} Returns `true` if all elements pass the predicate check,
   *  else `false`.
   */
  function arrayEvery(array, predicate) {
    var index = -1,
        length = array.length;

    while (++index < length) {
      if (!predicate(array[index], index, array)) {
        return false;
      }
    }
    return true;
  }

  /**
   * A specialized version of `_.filter` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} predicate The function invoked per iteration.
   * @returns {Array} Returns the new filtered array.
   */
  function arrayFilter(array, predicate) {
    var index = -1,
        length = array.length,
        resIndex = 0,
        result = [];

    while (++index < length) {
      var value = array[index];
      if (predicate(value, index, array)) {
        result[resIndex++] = value;
      }
    }
    return result;
  }

  /**
   * A specialized version of `_.includes` for arrays without support for
   * specifying an index to search from.
   *
   * @private
   * @param {Array} array The array to search.
   * @param {*} target The value to search for.
   * @returns {boolean} Returns `true` if `target` is found, else `false`.
   */
  function arrayIncludes(array, value) {
    return !!array.length && baseIndexOf(array, value, 0) > -1;
  }

  /**
   * This function is like `arrayIncludes` except that it accepts a comparator.
   *
   * @private
   * @param {Array} array The array to search.
   * @param {*} target The value to search for.
   * @param {Function} comparator The comparator invoked per element.
   * @returns {boolean} Returns `true` if `target` is found, else `false`.
   */
  function arrayIncludesWith(array, value, comparator) {
    var index = -1,
        length = array.length;

    while (++index < length) {
      if (comparator(value, array[index])) {
        return true;
      }
    }
    return false;
  }

  /**
   * A specialized version of `_.map` for arrays without support for iteratee
   * shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns the new mapped array.
   */
  function arrayMap(array, iteratee) {
    var index = -1,
        length = array.length,
        result = Array(length);

    while (++index < length) {
      result[index] = iteratee(array[index], index, array);
    }
    return result;
  }

  /**
   * Appends the elements of `values` to `array`.
   *
   * @private
   * @param {Array} array The array to modify.
   * @param {Array} values The values to append.
   * @returns {Array} Returns `array`.
   */
  function arrayPush(array, values) {
    var index = -1,
        length = values.length,
        offset = array.length;

    while (++index < length) {
      array[offset + index] = values[index];
    }
    return array;
  }

  /**
   * A specialized version of `_.reduce` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @param {*} [accumulator] The initial value.
   * @param {boolean} [initAccum] Specify using the first element of `array` as
   *  the initial value.
   * @returns {*} Returns the accumulated value.
   */
  function arrayReduce(array, iteratee, accumulator, initAccum) {
    var index = -1,
        length = array.length;

    if (initAccum && length) {
      accumulator = array[++index];
    }
    while (++index < length) {
      accumulator = iteratee(accumulator, array[index], index, array);
    }
    return accumulator;
  }

  /**
   * A specialized version of `_.reduceRight` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @param {*} [accumulator] The initial value.
   * @param {boolean} [initAccum] Specify using the last element of `array` as
   *  the initial value.
   * @returns {*} Returns the accumulated value.
   */
  function arrayReduceRight(array, iteratee, accumulator, initAccum) {
    var length = array.length;
    if (initAccum && length) {
      accumulator = array[--length];
    }
    while (length--) {
      accumulator = iteratee(accumulator, array[length], length, array);
    }
    return accumulator;
  }

  /**
   * A specialized version of `_.some` for arrays without support for iteratee
   * shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} predicate The function invoked per iteration.
   * @returns {boolean} Returns `true` if any element passes the predicate check,
   *  else `false`.
   */
  function arraySome(array, predicate) {
    var index = -1,
        length = array.length;

    while (++index < length) {
      if (predicate(array[index], index, array)) {
        return true;
      }
    }
    return false;
  }

  /**
   * The base implementation of methods like `_.find` and `_.findKey`, without
   * support for iteratee shorthands, which iterates over `collection` using
   * `eachFunc`.
   *
   * @private
   * @param {Array|Object} collection The collection to search.
   * @param {Function} predicate The function invoked per iteration.
   * @param {Function} eachFunc The function to iterate over `collection`.
   * @param {boolean} [retKey] Specify returning the key of the found element
   *  instead of the element itself.
   * @returns {*} Returns the found element or its key, else `undefined`.
   */
  function baseFind(collection, predicate, eachFunc, retKey) {
    var result;
    eachFunc(collection, function(value, key, collection) {
      if (predicate(value, key, collection)) {
        result = retKey ? key : value;
        return false;
      }
    });
    return result;
  }

  /**
   * The base implementation of `_.findIndex` and `_.findLastIndex` without
   * support for iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to search.
   * @param {Function} predicate The function invoked per iteration.
   * @param {boolean} [fromRight] Specify iterating from right to left.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function baseFindIndex(array, predicate, fromRight) {
    var length = array.length,
        index = fromRight ? length : -1;

    while ((fromRight ? index-- : ++index < length)) {
      if (predicate(array[index], index, array)) {
        return index;
      }
    }
    return -1;
  }

  /**
   * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
   *
   * @private
   * @param {Array} array The array to search.
   * @param {*} value The value to search for.
   * @param {number} fromIndex The index to search from.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function baseIndexOf(array, value, fromIndex) {
    if (value !== value) {
      return indexOfNaN(array, fromIndex);
    }
    var index = fromIndex - 1,
        length = array.length;

    while (++index < length) {
      if (array[index] === value) {
        return index;
      }
    }
    return -1;
  }

  /**
   * This function is like `baseIndexOf` except that it accepts a comparator.
   *
   * @private
   * @param {Array} array The array to search.
   * @param {*} value The value to search for.
   * @param {number} fromIndex The index to search from.
   * @param {Function} comparator The comparator invoked per element.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function baseIndexOfWith(array, value, fromIndex, comparator) {
    var index = fromIndex - 1,
        length = array.length;

    while (++index < length) {
      if (comparator(array[index], value)) {
        return index;
      }
    }
    return -1;
  }

  /**
   * The base implementation of `_.mean` and `_.meanBy` without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {number} Returns the mean.
   */
  function baseMean(array, iteratee) {
    var length = array ? array.length : 0;
    return length ? (baseSum(array, iteratee) / length) : NAN;
  }

  /**
   * The base implementation of `_.reduce` and `_.reduceRight`, without support
   * for iteratee shorthands, which iterates over `collection` using `eachFunc`.
   *
   * @private
   * @param {Array|Object} collection The collection to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @param {*} accumulator The initial value.
   * @param {boolean} initAccum Specify using the first or last element of
   *  `collection` as the initial value.
   * @param {Function} eachFunc The function to iterate over `collection`.
   * @returns {*} Returns the accumulated value.
   */
  function baseReduce(collection, iteratee, accumulator, initAccum, eachFunc) {
    eachFunc(collection, function(value, index, collection) {
      accumulator = initAccum
        ? (initAccum = false, value)
        : iteratee(accumulator, value, index, collection);
    });
    return accumulator;
  }

  /**
   * The base implementation of `_.sortBy` which uses `comparer` to define the
   * sort order of `array` and replaces criteria objects with their corresponding
   * values.
   *
   * @private
   * @param {Array} array The array to sort.
   * @param {Function} comparer The function to define sort order.
   * @returns {Array} Returns `array`.
   */
  function baseSortBy(array, comparer) {
    var length = array.length;

    array.sort(comparer);
    while (length--) {
      array[length] = array[length].value;
    }
    return array;
  }

  /**
   * The base implementation of `_.sum` and `_.sumBy` without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {number} Returns the sum.
   */
  function baseSum(array, iteratee) {
    var result,
        index = -1,
        length = array.length;

    while (++index < length) {
      var current = iteratee(array[index]);
      if (current !== undefined) {
        result = result === undefined ? current : (result + current);
      }
    }
    return result;
  }

  /**
   * The base implementation of `_.times` without support for iteratee shorthands
   * or max array length checks.
   *
   * @private
   * @param {number} n The number of times to invoke `iteratee`.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns the array of results.
   */
  function baseTimes(n, iteratee) {
    var index = -1,
        result = Array(n);

    while (++index < n) {
      result[index] = iteratee(index);
    }
    return result;
  }

  /**
   * The base implementation of `_.toPairs` and `_.toPairsIn` which creates an array
   * of key-value pairs for `object` corresponding to the property names of `props`.
   *
   * @private
   * @param {Object} object The object to query.
   * @param {Array} props The property names to get values for.
   * @returns {Object} Returns the key-value pairs.
   */
  function baseToPairs(object, props) {
    return arrayMap(props, function(key) {
      return [key, object[key]];
    });
  }

  /**
   * The base implementation of `_.unary` without support for storing wrapper metadata.
   *
   * @private
   * @param {Function} func The function to cap arguments for.
   * @returns {Function} Returns the new capped function.
   */
  function baseUnary(func) {
    return function(value) {
      return func(value);
    };
  }

  /**
   * The base implementation of `_.values` and `_.valuesIn` which creates an
   * array of `object` property values corresponding to the property names
   * of `props`.
   *
   * @private
   * @param {Object} object The object to query.
   * @param {Array} props The property names to get values for.
   * @returns {Object} Returns the array of property values.
   */
  function baseValues(object, props) {
    return arrayMap(props, function(key) {
      return object[key];
    });
  }

  /**
   * Checks if a cache value for `key` exists.
   *
   * @private
   * @param {Object} cache The cache to query.
   * @param {string} key The key of the entry to check.
   * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
   */
  function cacheHas(cache, key) {
    return cache.has(key);
  }

  /**
   * Used by `_.trim` and `_.trimStart` to get the index of the first string symbol
   * that is not found in the character symbols.
   *
   * @private
   * @param {Array} strSymbols The string symbols to inspect.
   * @param {Array} chrSymbols The character symbols to find.
   * @returns {number} Returns the index of the first unmatched string symbol.
   */
  function charsStartIndex(strSymbols, chrSymbols) {
    var index = -1,
        length = strSymbols.length;

    while (++index < length && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
    return index;
  }

  /**
   * Used by `_.trim` and `_.trimEnd` to get the index of the last string symbol
   * that is not found in the character symbols.
   *
   * @private
   * @param {Array} strSymbols The string symbols to inspect.
   * @param {Array} chrSymbols The character symbols to find.
   * @returns {number} Returns the index of the last unmatched string symbol.
   */
  function charsEndIndex(strSymbols, chrSymbols) {
    var index = strSymbols.length;

    while (index-- && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
    return index;
  }

  /**
   * Checks if `value` is a global object.
   *
   * @private
   * @param {*} value The value to check.
   * @returns {null|Object} Returns `value` if it's a global object, else `null`.
   */
  function checkGlobal(value) {
    return (value && value.Object === Object) ? value : null;
  }

  /**
   * Gets the number of `placeholder` occurrences in `array`.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} placeholder The placeholder to search for.
   * @returns {number} Returns the placeholder count.
   */
  function countHolders(array, placeholder) {
    var length = array.length,
        result = 0;

    while (length--) {
      if (array[length] === placeholder) {
        result++;
      }
    }
    return result;
  }

  /**
   * Used by `_.deburr` to convert latin-1 supplementary letters to basic latin letters.
   *
   * @private
   * @param {string} letter The matched letter to deburr.
   * @returns {string} Returns the deburred letter.
   */
  function deburrLetter(letter) {
    return deburredLetters[letter];
  }

  /**
   * Used by `_.escape` to convert characters to HTML entities.
   *
   * @private
   * @param {string} chr The matched character to escape.
   * @returns {string} Returns the escaped character.
   */
  function escapeHtmlChar(chr) {
    return htmlEscapes[chr];
  }

  /**
   * Used by `_.template` to escape characters for inclusion in compiled string literals.
   *
   * @private
   * @param {string} chr The matched character to escape.
   * @returns {string} Returns the escaped character.
   */
  function escapeStringChar(chr) {
    return '\\' + stringEscapes[chr];
  }

  /**
   * Gets the index at which the first occurrence of `NaN` is found in `array`.
   *
   * @private
   * @param {Array} array The array to search.
   * @param {number} fromIndex The index to search from.
   * @param {boolean} [fromRight] Specify iterating from right to left.
   * @returns {number} Returns the index of the matched `NaN`, else `-1`.
   */
  function indexOfNaN(array, fromIndex, fromRight) {
    var length = array.length,
        index = fromIndex + (fromRight ? 0 : -1);

    while ((fromRight ? index-- : ++index < length)) {
      var other = array[index];
      if (other !== other) {
        return index;
      }
    }
    return -1;
  }

  /**
   * Checks if `value` is a host object in IE < 9.
   *
   * @private
   * @param {*} value The value to check.
   * @returns {boolean} Returns `true` if `value` is a host object, else `false`.
   */
  function isHostObject(value) {
    // Many host objects are `Object` objects that can coerce to strings
    // despite having improperly defined `toString` methods.
    var result = false;
    if (value != null && typeof value.toString != 'function') {
      try {
        result = !!(value + '');
      } catch (e) {}
    }
    return result;
  }

  /**
   * Converts `iterator` to an array.
   *
   * @private
   * @param {Object} iterator The iterator to convert.
   * @returns {Array} Returns the converted array.
   */
  function iteratorToArray(iterator) {
    var data,
        result = [];

    while (!(data = iterator.next()).done) {
      result.push(data.value);
    }
    return result;
  }

  /**
   * Converts `map` to its key-value pairs.
   *
   * @private
   * @param {Object} map The map to convert.
   * @returns {Array} Returns the key-value pairs.
   */
  function mapToArray(map) {
    var index = -1,
        result = Array(map.size);

    map.forEach(function(value, key) {
      result[++index] = [key, value];
    });
    return result;
  }

  /**
   * Replaces all `placeholder` elements in `array` with an internal placeholder
   * and returns an array of their indexes.
   *
   * @private
   * @param {Array} array The array to modify.
   * @param {*} placeholder The placeholder to replace.
   * @returns {Array} Returns the new array of placeholder indexes.
   */
  function replaceHolders(array, placeholder) {
    var index = -1,
        length = array.length,
        resIndex = 0,
        result = [];

    while (++index < length) {
      var value = array[index];
      if (value === placeholder || value === PLACEHOLDER) {
        array[index] = PLACEHOLDER;
        result[resIndex++] = index;
      }
    }
    return result;
  }

  /**
   * Converts `set` to an array of its values.
   *
   * @private
   * @param {Object} set The set to convert.
   * @returns {Array} Returns the values.
   */
  function setToArray(set) {
    var index = -1,
        result = Array(set.size);

    set.forEach(function(value) {
      result[++index] = value;
    });
    return result;
  }

  /**
   * Converts `set` to its value-value pairs.
   *
   * @private
   * @param {Object} set The set to convert.
   * @returns {Array} Returns the value-value pairs.
   */
  function setToPairs(set) {
    var index = -1,
        result = Array(set.size);

    set.forEach(function(value) {
      result[++index] = [value, value];
    });
    return result;
  }

  /**
   * Gets the number of symbols in `string`.
   *
   * @private
   * @param {string} string The string to inspect.
   * @returns {number} Returns the string size.
   */
  function stringSize(string) {
    if (!(string && reHasComplexSymbol.test(string))) {
      return string.length;
    }
    var result = reComplexSymbol.lastIndex = 0;
    while (reComplexSymbol.test(string)) {
      result++;
    }
    return result;
  }

  /**
   * Converts `string` to an array.
   *
   * @private
   * @param {string} string The string to convert.
   * @returns {Array} Returns the converted array.
   */
  function stringToArray(string) {
    return string.match(reComplexSymbol);
  }

  /**
   * Used by `_.unescape` to convert HTML entities to characters.
   *
   * @private
   * @param {string} chr The matched character to unescape.
   * @returns {string} Returns the unescaped character.
   */
  function unescapeHtmlChar(chr) {
    return htmlUnescapes[chr];
  }

  /*--------------------------------------------------------------------------*/

  /**
   * Create a new pristine `lodash` function using the `context` object.
   *
   * @static
   * @memberOf _
   * @since 1.1.0
   * @category Util
   * @param {Object} [context=root] The context object.
   * @returns {Function} Returns a new `lodash` function.
   * @example
   *
   * _.mixin({ 'foo': _.constant('foo') });
   *
   * var lodash = _.runInContext();
   * lodash.mixin({ 'bar': lodash.constant('bar') });
   *
   * _.isFunction(_.foo);
   * // => true
   * _.isFunction(_.bar);
   * // => false
   *
   * lodash.isFunction(lodash.foo);
   * // => false
   * lodash.isFunction(lodash.bar);
   * // => true
   *
   * // Use `context` to mock `Date#getTime` use in `_.now`.
   * var mock = _.runInContext({
   *   'Date': function() {
   *     return { 'getTime': getTimeMock };
   *   }
   * });
   *
   * // Create a suped-up `defer` in Node.js.
   * var defer = _.runInContext({ 'setTimeout': setImmediate }).defer;
   */
  function runInContext(context) {
    context = context ? _.defaults({}, context, _.pick(root, contextProps)) : root;

    /** Built-in constructor references. */
    var Date = context.Date,
        Error = context.Error,
        Math = context.Math,
        RegExp = context.RegExp,
        TypeError = context.TypeError;

    /** Used for built-in method references. */
    var arrayProto = context.Array.prototype,
        objectProto = context.Object.prototype,
        stringProto = context.String.prototype;

    /** Used to resolve the decompiled source of functions. */
    var funcToString = context.Function.prototype.toString;

    /** Used to check objects for own properties. */
    var hasOwnProperty = objectProto.hasOwnProperty;

    /** Used to generate unique IDs. */
    var idCounter = 0;

    /** Used to infer the `Object` constructor. */
    var objectCtorString = funcToString.call(Object);

    /**
     * Used to resolve the
     * [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
     * of values.
     */
    var objectToString = objectProto.toString;

    /** Used to restore the original `_` reference in `_.noConflict`. */
    var oldDash = root._;

    /** Used to detect if a method is native. */
    var reIsNative = RegExp('^' +
      funcToString.call(hasOwnProperty).replace(reRegExpChar, '\\$&')
      .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
    );

    /** Built-in value references. */
    var Buffer = moduleExports ? context.Buffer : undefined,
        Reflect = context.Reflect,
        Symbol = context.Symbol,
        Uint8Array = context.Uint8Array,
        clearTimeout = context.clearTimeout,
        enumerate = Reflect ? Reflect.enumerate : undefined,
        getOwnPropertySymbols = Object.getOwnPropertySymbols,
        iteratorSymbol = typeof (iteratorSymbol = Symbol && Symbol.iterator) == 'symbol' ? iteratorSymbol : undefined,
        objectCreate = Object.create,
        propertyIsEnumerable = objectProto.propertyIsEnumerable,
        setTimeout = context.setTimeout,
        splice = arrayProto.splice;

    /* Built-in method references for those with the same name as other `lodash` methods. */
    var nativeCeil = Math.ceil,
        nativeFloor = Math.floor,
        nativeGetPrototype = Object.getPrototypeOf,
        nativeIsFinite = context.isFinite,
        nativeJoin = arrayProto.join,
        nativeKeys = Object.keys,
        nativeMax = Math.max,
        nativeMin = Math.min,
        nativeParseInt = context.parseInt,
        nativeRandom = Math.random,
        nativeReplace = stringProto.replace,
        nativeReverse = arrayProto.reverse,
        nativeSplit = stringProto.split;

    /* Built-in method references that are verified to be native. */
    var DataView = getNative(context, 'DataView'),
        Map = getNative(context, 'Map'),
        Promise = getNative(context, 'Promise'),
        Set = getNative(context, 'Set'),
        WeakMap = getNative(context, 'WeakMap'),
        nativeCreate = getNative(Object, 'create');

    /** Used to store function metadata. */
    var metaMap = WeakMap && new WeakMap;

    /** Detect if properties shadowing those on `Object.prototype` are non-enumerable. */
    var nonEnumShadows = !propertyIsEnumerable.call({ 'valueOf': 1 }, 'valueOf');

    /** Used to lookup unminified function names. */
    var realNames = {};

    /** Used to detect maps, sets, and weakmaps. */
    var dataViewCtorString = toSource(DataView),
        mapCtorString = toSource(Map),
        promiseCtorString = toSource(Promise),
        setCtorString = toSource(Set),
        weakMapCtorString = toSource(WeakMap);

    /** Used to convert symbols to primitives and strings. */
    var symbolProto = Symbol ? Symbol.prototype : undefined,
        symbolValueOf = symbolProto ? symbolProto.valueOf : undefined,
        symbolToString = symbolProto ? symbolProto.toString : undefined;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a `lodash` object which wraps `value` to enable implicit method
     * chain sequences. Methods that operate on and return arrays, collections,
     * and functions can be chained together. Methods that retrieve a single value
     * or may return a primitive value will automatically end the chain sequence
     * and return the unwrapped value. Otherwise, the value must be unwrapped
     * with `_#value`.
     *
     * Explicit chain sequences, which must be unwrapped with `_#value`, may be
     * enabled using `_.chain`.
     *
     * The execution of chained methods is lazy, that is, it's deferred until
     * `_#value` is implicitly or explicitly called.
     *
     * Lazy evaluation allows several methods to support shortcut fusion.
     * Shortcut fusion is an optimization to merge iteratee calls; this avoids
     * the creation of intermediate arrays and can greatly reduce the number of
     * iteratee executions. Sections of a chain sequence qualify for shortcut
     * fusion if the section is applied to an array of at least `200` elements
     * and any iteratees accept only one argument. The heuristic for whether a
     * section qualifies for shortcut fusion is subject to change.
     *
     * Chaining is supported in custom builds as long as the `_#value` method is
     * directly or indirectly included in the build.
     *
     * In addition to lodash methods, wrappers have `Array` and `String` methods.
     *
     * The wrapper `Array` methods are:
     * `concat`, `join`, `pop`, `push`, `shift`, `sort`, `splice`, and `unshift`
     *
     * The wrapper `String` methods are:
     * `replace` and `split`
     *
     * The wrapper methods that support shortcut fusion are:
     * `at`, `compact`, `drop`, `dropRight`, `dropWhile`, `filter`, `find`,
     * `findLast`, `head`, `initial`, `last`, `map`, `reject`, `reverse`, `slice`,
     * `tail`, `take`, `takeRight`, `takeRightWhile`, `takeWhile`, and `toArray`
     *
     * The chainable wrapper methods are:
     * `after`, `ary`, `assign`, `assignIn`, `assignInWith`, `assignWith`, `at`,
     * `before`, `bind`, `bindAll`, `bindKey`, `castArray`, `chain`, `chunk`,
     * `commit`, `compact`, `concat`, `conforms`, `constant`, `countBy`, `create`,
     * `curry`, `debounce`, `defaults`, `defaultsDeep`, `defer`, `delay`,
     * `difference`, `differenceBy`, `differenceWith`, `drop`, `dropRight`,
     * `dropRightWhile`, `dropWhile`, `extend`, `extendWith`, `fill`, `filter`,
     * `flatMap`, `flatMapDeep`, `flatMapDepth`, `flatten`, `flattenDeep`,
     * `flattenDepth`, `flip`, `flow`, `flowRight`, `fromPairs`, `functions`,
     * `functionsIn`, `groupBy`, `initial`, `intersection`, `intersectionBy`,
     * `intersectionWith`, `invert`, `invertBy`, `invokeMap`, `iteratee`, `keyBy`,
     * `keys`, `keysIn`, `map`, `mapKeys`, `mapValues`, `matches`, `matchesProperty`,
     * `memoize`, `merge`, `mergeWith`, `method`, `methodOf`, `mixin`, `negate`,
     * `nthArg`, `omit`, `omitBy`, `once`, `orderBy`, `over`, `overArgs`,
     * `overEvery`, `overSome`, `partial`, `partialRight`, `partition`, `pick`,
     * `pickBy`, `plant`, `property`, `propertyOf`, `pull`, `pullAll`, `pullAllBy`,
     * `pullAllWith`, `pullAt`, `push`, `range`, `rangeRight`, `rearg`, `reject`,
     * `remove`, `rest`, `reverse`, `sampleSize`, `set`, `setWith`, `shuffle`,
     * `slice`, `sort`, `sortBy`, `splice`, `spread`, `tail`, `take`, `takeRight`,
     * `takeRightWhile`, `takeWhile`, `tap`, `throttle`, `thru`, `toArray`,
     * `toPairs`, `toPairsIn`, `toPath`, `toPlainObject`, `transform`, `unary`,
     * `union`, `unionBy`, `unionWith`, `uniq`, `uniqBy`, `uniqWith`, `unset`,
     * `unshift`, `unzip`, `unzipWith`, `update`, `updateWith`, `values`,
     * `valuesIn`, `without`, `wrap`, `xor`, `xorBy`, `xorWith`, `zip`,
     * `zipObject`, `zipObjectDeep`, and `zipWith`
     *
     * The wrapper methods that are **not** chainable by default are:
     * `add`, `attempt`, `camelCase`, `capitalize`, `ceil`, `clamp`, `clone`,
     * `cloneDeep`, `cloneDeepWith`, `cloneWith`, `deburr`, `divide`, `each`,
     * `eachRight`, `endsWith`, `eq`, `escape`, `escapeRegExp`, `every`, `find`,
     * `findIndex`, `findKey`, `findLast`, `findLastIndex`, `findLastKey`, `first`,
     * `floor`, `forEach`, `forEachRight`, `forIn`, `forInRight`, `forOwn`,
     * `forOwnRight`, `get`, `gt`, `gte`, `has`, `hasIn`, `head`, `identity`,
     * `includes`, `indexOf`, `inRange`, `invoke`, `isArguments`, `isArray`,
     * `isArrayBuffer`, `isArrayLike`, `isArrayLikeObject`, `isBoolean`,
     * `isBuffer`, `isDate`, `isElement`, `isEmpty`, `isEqual`, `isEqualWith`,
     * `isError`, `isFinite`, `isFunction`, `isInteger`, `isLength`, `isMap`,
     * `isMatch`, `isMatchWith`, `isNaN`, `isNative`, `isNil`, `isNull`, `isNumber`,
     * `isObject`, `isObjectLike`, `isPlainObject`, `isRegExp`, `isSafeInteger`,
     * `isSet`, `isString`, `isUndefined`, `isTypedArray`, `isWeakMap`, `isWeakSet`,
     * `join`, `kebabCase`, `last`, `lastIndexOf`, `lowerCase`, `lowerFirst`,
     * `lt`, `lte`, `max`, `maxBy`, `mean`, `meanBy`, `min`, `minBy`, `multiply`,
     * `noConflict`, `noop`, `now`, `nth`, `pad`, `padEnd`, `padStart`, `parseInt`,
     * `pop`, `random`, `reduce`, `reduceRight`, `repeat`, `result`, `round`,
     * `runInContext`, `sample`, `shift`, `size`, `snakeCase`, `some`, `sortedIndex`,
     * `sortedIndexBy`, `sortedLastIndex`, `sortedLastIndexBy`, `startCase`,
     * `startsWith`, `subtract`, `sum`, `sumBy`, `template`, `times`, `toFinite`,
     * `toInteger`, `toJSON`, `toLength`, `toLower`, `toNumber`, `toSafeInteger`,
     * `toString`, `toUpper`, `trim`, `trimEnd`, `trimStart`, `truncate`, `unescape`,
     * `uniqueId`, `upperCase`, `upperFirst`, `value`, and `words`
     *
     * @name _
     * @constructor
     * @category Seq
     * @param {*} value The value to wrap in a `lodash` instance.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var wrapped = _([1, 2, 3]);
     *
     * // Returns an unwrapped value.
     * wrapped.reduce(_.add);
     * // => 6
     *
     * // Returns a wrapped value.
     * var squares = wrapped.map(square);
     *
     * _.isArray(squares);
     * // => false
     *
     * _.isArray(squares.value());
     * // => true
     */
    function lodash(value) {
      if (isObjectLike(value) && !isArray(value) && !(value instanceof LazyWrapper)) {
        if (value instanceof LodashWrapper) {
          return value;
        }
        if (hasOwnProperty.call(value, '__wrapped__')) {
          return wrapperClone(value);
        }
      }
      return new LodashWrapper(value);
    }

    /**
     * The function whose prototype chain sequence wrappers inherit from.
     *
     * @private
     */
    function baseLodash() {
      // No operation performed.
    }

    /**
     * The base constructor for creating `lodash` wrapper objects.
     *
     * @private
     * @param {*} value The value to wrap.
     * @param {boolean} [chainAll] Enable explicit method chain sequences.
     */
    function LodashWrapper(value, chainAll) {
      this.__wrapped__ = value;
      this.__actions__ = [];
      this.__chain__ = !!chainAll;
      this.__index__ = 0;
      this.__values__ = undefined;
    }

    /**
     * By default, the template delimiters used by lodash are like those in
     * embedded Ruby (ERB). Change the following template settings to use
     * alternative delimiters.
     *
     * @static
     * @memberOf _
     * @type {Object}
     */
    lodash.templateSettings = {

      /**
       * Used to detect `data` property values to be HTML-escaped.
       *
       * @memberOf _.templateSettings
       * @type {RegExp}
       */
      'escape': reEscape,

      /**
       * Used to detect code to be evaluated.
       *
       * @memberOf _.templateSettings
       * @type {RegExp}
       */
      'evaluate': reEvaluate,

      /**
       * Used to detect `data` property values to inject.
       *
       * @memberOf _.templateSettings
       * @type {RegExp}
       */
      'interpolate': reInterpolate,

      /**
       * Used to reference the data object in the template text.
       *
       * @memberOf _.templateSettings
       * @type {string}
       */
      'variable': '',

      /**
       * Used to import variables into the compiled template.
       *
       * @memberOf _.templateSettings
       * @type {Object}
       */
      'imports': {

        /**
         * A reference to the `lodash` function.
         *
         * @memberOf _.templateSettings.imports
         * @type {Function}
         */
        '_': lodash
      }
    };

    // Ensure wrappers are instances of `baseLodash`.
    lodash.prototype = baseLodash.prototype;
    lodash.prototype.constructor = lodash;

    LodashWrapper.prototype = baseCreate(baseLodash.prototype);
    LodashWrapper.prototype.constructor = LodashWrapper;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a lazy wrapper object which wraps `value` to enable lazy evaluation.
     *
     * @private
     * @constructor
     * @param {*} value The value to wrap.
     */
    function LazyWrapper(value) {
      this.__wrapped__ = value;
      this.__actions__ = [];
      this.__dir__ = 1;
      this.__filtered__ = false;
      this.__iteratees__ = [];
      this.__takeCount__ = MAX_ARRAY_LENGTH;
      this.__views__ = [];
    }

    /**
     * Creates a clone of the lazy wrapper object.
     *
     * @private
     * @name clone
     * @memberOf LazyWrapper
     * @returns {Object} Returns the cloned `LazyWrapper` object.
     */
    function lazyClone() {
      var result = new LazyWrapper(this.__wrapped__);
      result.__actions__ = copyArray(this.__actions__);
      result.__dir__ = this.__dir__;
      result.__filtered__ = this.__filtered__;
      result.__iteratees__ = copyArray(this.__iteratees__);
      result.__takeCount__ = this.__takeCount__;
      result.__views__ = copyArray(this.__views__);
      return result;
    }

    /**
     * Reverses the direction of lazy iteration.
     *
     * @private
     * @name reverse
     * @memberOf LazyWrapper
     * @returns {Object} Returns the new reversed `LazyWrapper` object.
     */
    function lazyReverse() {
      if (this.__filtered__) {
        var result = new LazyWrapper(this);
        result.__dir__ = -1;
        result.__filtered__ = true;
      } else {
        result = this.clone();
        result.__dir__ *= -1;
      }
      return result;
    }

    /**
     * Extracts the unwrapped value from its lazy wrapper.
     *
     * @private
     * @name value
     * @memberOf LazyWrapper
     * @returns {*} Returns the unwrapped value.
     */
    function lazyValue() {
      var array = this.__wrapped__.value(),
          dir = this.__dir__,
          isArr = isArray(array),
          isRight = dir < 0,
          arrLength = isArr ? array.length : 0,
          view = getView(0, arrLength, this.__views__),
          start = view.start,
          end = view.end,
          length = end - start,
          index = isRight ? end : (start - 1),
          iteratees = this.__iteratees__,
          iterLength = iteratees.length,
          resIndex = 0,
          takeCount = nativeMin(length, this.__takeCount__);

      if (!isArr || arrLength < LARGE_ARRAY_SIZE ||
          (arrLength == length && takeCount == length)) {
        return baseWrapperValue(array, this.__actions__);
      }
      var result = [];

      outer:
      while (length-- && resIndex < takeCount) {
        index += dir;

        var iterIndex = -1,
            value = array[index];

        while (++iterIndex < iterLength) {
          var data = iteratees[iterIndex],
              iteratee = data.iteratee,
              type = data.type,
              computed = iteratee(value);

          if (type == LAZY_MAP_FLAG) {
            value = computed;
          } else if (!computed) {
            if (type == LAZY_FILTER_FLAG) {
              continue outer;
            } else {
              break outer;
            }
          }
        }
        result[resIndex++] = value;
      }
      return result;
    }

    // Ensure `LazyWrapper` is an instance of `baseLodash`.
    LazyWrapper.prototype = baseCreate(baseLodash.prototype);
    LazyWrapper.prototype.constructor = LazyWrapper;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a hash object.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function Hash(entries) {
      var index = -1,
          length = entries ? entries.length : 0;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the hash.
     *
     * @private
     * @name clear
     * @memberOf Hash
     */
    function hashClear() {
      this.__data__ = nativeCreate ? nativeCreate(null) : {};
    }

    /**
     * Removes `key` and its value from the hash.
     *
     * @private
     * @name delete
     * @memberOf Hash
     * @param {Object} hash The hash to modify.
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function hashDelete(key) {
      return this.has(key) && delete this.__data__[key];
    }

    /**
     * Gets the hash value for `key`.
     *
     * @private
     * @name get
     * @memberOf Hash
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function hashGet(key) {
      var data = this.__data__;
      if (nativeCreate) {
        var result = data[key];
        return result === HASH_UNDEFINED ? undefined : result;
      }
      return hasOwnProperty.call(data, key) ? data[key] : undefined;
    }

    /**
     * Checks if a hash value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf Hash
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function hashHas(key) {
      var data = this.__data__;
      return nativeCreate ? data[key] !== undefined : hasOwnProperty.call(data, key);
    }

    /**
     * Sets the hash `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf Hash
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the hash instance.
     */
    function hashSet(key, value) {
      var data = this.__data__;
      data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED : value;
      return this;
    }

    // Add methods to `Hash`.
    Hash.prototype.clear = hashClear;
    Hash.prototype['delete'] = hashDelete;
    Hash.prototype.get = hashGet;
    Hash.prototype.has = hashHas;
    Hash.prototype.set = hashSet;

    /*------------------------------------------------------------------------*/

    /**
     * Creates an list cache object.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function ListCache(entries) {
      var index = -1,
          length = entries ? entries.length : 0;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the list cache.
     *
     * @private
     * @name clear
     * @memberOf ListCache
     */
    function listCacheClear() {
      this.__data__ = [];
    }

    /**
     * Removes `key` and its value from the list cache.
     *
     * @private
     * @name delete
     * @memberOf ListCache
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function listCacheDelete(key) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      if (index < 0) {
        return false;
      }
      var lastIndex = data.length - 1;
      if (index == lastIndex) {
        data.pop();
      } else {
        splice.call(data, index, 1);
      }
      return true;
    }

    /**
     * Gets the list cache value for `key`.
     *
     * @private
     * @name get
     * @memberOf ListCache
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function listCacheGet(key) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      return index < 0 ? undefined : data[index][1];
    }

    /**
     * Checks if a list cache value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf ListCache
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function listCacheHas(key) {
      return assocIndexOf(this.__data__, key) > -1;
    }

    /**
     * Sets the list cache `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf ListCache
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the list cache instance.
     */
    function listCacheSet(key, value) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      if (index < 0) {
        data.push([key, value]);
      } else {
        data[index][1] = value;
      }
      return this;
    }

    // Add methods to `ListCache`.
    ListCache.prototype.clear = listCacheClear;
    ListCache.prototype['delete'] = listCacheDelete;
    ListCache.prototype.get = listCacheGet;
    ListCache.prototype.has = listCacheHas;
    ListCache.prototype.set = listCacheSet;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a map cache object to store key-value pairs.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function MapCache(entries) {
      var index = -1,
          length = entries ? entries.length : 0;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the map.
     *
     * @private
     * @name clear
     * @memberOf MapCache
     */
    function mapCacheClear() {
      this.__data__ = {
        'hash': new Hash,
        'map': new (Map || ListCache),
        'string': new Hash
      };
    }

    /**
     * Removes `key` and its value from the map.
     *
     * @private
     * @name delete
     * @memberOf MapCache
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function mapCacheDelete(key) {
      return getMapData(this, key)['delete'](key);
    }

    /**
     * Gets the map value for `key`.
     *
     * @private
     * @name get
     * @memberOf MapCache
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function mapCacheGet(key) {
      return getMapData(this, key).get(key);
    }

    /**
     * Checks if a map value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf MapCache
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function mapCacheHas(key) {
      return getMapData(this, key).has(key);
    }

    /**
     * Sets the map `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf MapCache
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the map cache instance.
     */
    function mapCacheSet(key, value) {
      getMapData(this, key).set(key, value);
      return this;
    }

    // Add methods to `MapCache`.
    MapCache.prototype.clear = mapCacheClear;
    MapCache.prototype['delete'] = mapCacheDelete;
    MapCache.prototype.get = mapCacheGet;
    MapCache.prototype.has = mapCacheHas;
    MapCache.prototype.set = mapCacheSet;

    /*------------------------------------------------------------------------*/

    /**
     *
     * Creates an array cache object to store unique values.
     *
     * @private
     * @constructor
     * @param {Array} [values] The values to cache.
     */
    function SetCache(values) {
      var index = -1,
          length = values ? values.length : 0;

      this.__data__ = new MapCache;
      while (++index < length) {
        this.add(values[index]);
      }
    }

    /**
     * Adds `value` to the array cache.
     *
     * @private
     * @name add
     * @memberOf SetCache
     * @alias push
     * @param {*} value The value to cache.
     * @returns {Object} Returns the cache instance.
     */
    function setCacheAdd(value) {
      this.__data__.set(value, HASH_UNDEFINED);
      return this;
    }

    /**
     * Checks if `value` is in the array cache.
     *
     * @private
     * @name has
     * @memberOf SetCache
     * @param {*} value The value to search for.
     * @returns {number} Returns `true` if `value` is found, else `false`.
     */
    function setCacheHas(value) {
      return this.__data__.has(value);
    }

    // Add methods to `SetCache`.
    SetCache.prototype.add = SetCache.prototype.push = setCacheAdd;
    SetCache.prototype.has = setCacheHas;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a stack cache object to store key-value pairs.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function Stack(entries) {
      this.__data__ = new ListCache(entries);
    }

    /**
     * Removes all key-value entries from the stack.
     *
     * @private
     * @name clear
     * @memberOf Stack
     */
    function stackClear() {
      this.__data__ = new ListCache;
    }

    /**
     * Removes `key` and its value from the stack.
     *
     * @private
     * @name delete
     * @memberOf Stack
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function stackDelete(key) {
      return this.__data__['delete'](key);
    }

    /**
     * Gets the stack value for `key`.
     *
     * @private
     * @name get
     * @memberOf Stack
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function stackGet(key) {
      return this.__data__.get(key);
    }

    /**
     * Checks if a stack value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf Stack
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function stackHas(key) {
      return this.__data__.has(key);
    }

    /**
     * Sets the stack `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf Stack
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the stack cache instance.
     */
    function stackSet(key, value) {
      var cache = this.__data__;
      if (cache instanceof ListCache && cache.__data__.length == LARGE_ARRAY_SIZE) {
        cache = this.__data__ = new MapCache(cache.__data__);
      }
      cache.set(key, value);
      return this;
    }

    // Add methods to `Stack`.
    Stack.prototype.clear = stackClear;
    Stack.prototype['delete'] = stackDelete;
    Stack.prototype.get = stackGet;
    Stack.prototype.has = stackHas;
    Stack.prototype.set = stackSet;

    /*------------------------------------------------------------------------*/

    /**
     * Used by `_.defaults` to customize its `_.assignIn` use.
     *
     * @private
     * @param {*} objValue The destination value.
     * @param {*} srcValue The source value.
     * @param {string} key The key of the property to assign.
     * @param {Object} object The parent object of `objValue`.
     * @returns {*} Returns the value to assign.
     */
    function assignInDefaults(objValue, srcValue, key, object) {
      if (objValue === undefined ||
          (eq(objValue, objectProto[key]) && !hasOwnProperty.call(object, key))) {
        return srcValue;
      }
      return objValue;
    }

    /**
     * This function is like `assignValue` except that it doesn't assign
     * `undefined` values.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {string} key The key of the property to assign.
     * @param {*} value The value to assign.
     */
    function assignMergeValue(object, key, value) {
      if ((value !== undefined && !eq(object[key], value)) ||
          (typeof key == 'number' && value === undefined && !(key in object))) {
        object[key] = value;
      }
    }

    /**
     * Assigns `value` to `key` of `object` if the existing value is not equivalent
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {string} key The key of the property to assign.
     * @param {*} value The value to assign.
     */
    function assignValue(object, key, value) {
      var objValue = object[key];
      if (!(hasOwnProperty.call(object, key) && eq(objValue, value)) ||
          (value === undefined && !(key in object))) {
        object[key] = value;
      }
    }

    /**
     * Gets the index at which the `key` is found in `array` of key-value pairs.
     *
     * @private
     * @param {Array} array The array to search.
     * @param {*} key The key to search for.
     * @returns {number} Returns the index of the matched value, else `-1`.
     */
    function assocIndexOf(array, key) {
      var length = array.length;
      while (length--) {
        if (eq(array[length][0], key)) {
          return length;
        }
      }
      return -1;
    }

    /**
     * Aggregates elements of `collection` on `accumulator` with keys transformed
     * by `iteratee` and values set by `setter`.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} setter The function to set `accumulator` values.
     * @param {Function} iteratee The iteratee to transform keys.
     * @param {Object} accumulator The initial aggregated object.
     * @returns {Function} Returns `accumulator`.
     */
    function baseAggregator(collection, setter, iteratee, accumulator) {
      baseEach(collection, function(value, key, collection) {
        setter(accumulator, value, iteratee(value), collection);
      });
      return accumulator;
    }

    /**
     * The base implementation of `_.assign` without support for multiple sources
     * or `customizer` functions.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @returns {Object} Returns `object`.
     */
    function baseAssign(object, source) {
      return object && copyObject(source, keys(source), object);
    }

    /**
     * The base implementation of `_.at` without support for individual paths.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {string[]} paths The property paths of elements to pick.
     * @returns {Array} Returns the picked elements.
     */
    function baseAt(object, paths) {
      var index = -1,
          isNil = object == null,
          length = paths.length,
          result = Array(length);

      while (++index < length) {
        result[index] = isNil ? undefined : get(object, paths[index]);
      }
      return result;
    }

    /**
     * The base implementation of `_.clamp` which doesn't coerce arguments to numbers.
     *
     * @private
     * @param {number} number The number to clamp.
     * @param {number} [lower] The lower bound.
     * @param {number} upper The upper bound.
     * @returns {number} Returns the clamped number.
     */
    function baseClamp(number, lower, upper) {
      if (number === number) {
        if (upper !== undefined) {
          number = number <= upper ? number : upper;
        }
        if (lower !== undefined) {
          number = number >= lower ? number : lower;
        }
      }
      return number;
    }

    /**
     * The base implementation of `_.clone` and `_.cloneDeep` which tracks
     * traversed objects.
     *
     * @private
     * @param {*} value The value to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @param {boolean} [isFull] Specify a clone including symbols.
     * @param {Function} [customizer] The function to customize cloning.
     * @param {string} [key] The key of `value`.
     * @param {Object} [object] The parent object of `value`.
     * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
     * @returns {*} Returns the cloned value.
     */
    function baseClone(value, isDeep, isFull, customizer, key, object, stack) {
      var result;
      if (customizer) {
        result = object ? customizer(value, key, object, stack) : customizer(value);
      }
      if (result !== undefined) {
        return result;
      }
      if (!isObject(value)) {
        return value;
      }
      var isArr = isArray(value);
      if (isArr) {
        result = initCloneArray(value);
        if (!isDeep) {
          return copyArray(value, result);
        }
      } else {
        var tag = getTag(value),
            isFunc = tag == funcTag || tag == genTag;

        if (isBuffer(value)) {
          return cloneBuffer(value, isDeep);
        }
        if (tag == objectTag || tag == argsTag || (isFunc && !object)) {
          if (isHostObject(value)) {
            return object ? value : {};
          }
          result = initCloneObject(isFunc ? {} : value);
          if (!isDeep) {
            return copySymbols(value, baseAssign(result, value));
          }
        } else {
          if (!cloneableTags[tag]) {
            return object ? value : {};
          }
          result = initCloneByTag(value, tag, baseClone, isDeep);
        }
      }
      // Check for circular references and return its corresponding clone.
      stack || (stack = new Stack);
      var stacked = stack.get(value);
      if (stacked) {
        return stacked;
      }
      stack.set(value, result);

      if (!isArr) {
        var props = isFull ? getAllKeys(value) : keys(value);
      }
      // Recursively populate clone (susceptible to call stack limits).
      arrayEach(props || value, function(subValue, key) {
        if (props) {
          key = subValue;
          subValue = value[key];
        }
        assignValue(result, key, baseClone(subValue, isDeep, isFull, customizer, key, value, stack));
      });
      return result;
    }

    /**
     * The base implementation of `_.conforms` which doesn't clone `source`.
     *
     * @private
     * @param {Object} source The object of property predicates to conform to.
     * @returns {Function} Returns the new spec function.
     */
    function baseConforms(source) {
      var props = keys(source),
          length = props.length;

      return function(object) {
        if (object == null) {
          return !length;
        }
        var index = length;
        while (index--) {
          var key = props[index],
              predicate = source[key],
              value = object[key];

          if ((value === undefined &&
              !(key in Object(object))) || !predicate(value)) {
            return false;
          }
        }
        return true;
      };
    }

    /**
     * The base implementation of `_.create` without support for assigning
     * properties to the created object.
     *
     * @private
     * @param {Object} prototype The object to inherit from.
     * @returns {Object} Returns the new object.
     */
    function baseCreate(proto) {
      return isObject(proto) ? objectCreate(proto) : {};
    }

    /**
     * The base implementation of `_.delay` and `_.defer` which accepts an array
     * of `func` arguments.
     *
     * @private
     * @param {Function} func The function to delay.
     * @param {number} wait The number of milliseconds to delay invocation.
     * @param {Object} args The arguments to provide to `func`.
     * @returns {number} Returns the timer id.
     */
    function baseDelay(func, wait, args) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      return setTimeout(function() { func.apply(undefined, args); }, wait);
    }

    /**
     * The base implementation of methods like `_.difference` without support
     * for excluding multiple arrays or iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {Array} values The values to exclude.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     */
    function baseDifference(array, values, iteratee, comparator) {
      var index = -1,
          includes = arrayIncludes,
          isCommon = true,
          length = array.length,
          result = [],
          valuesLength = values.length;

      if (!length) {
        return result;
      }
      if (iteratee) {
        values = arrayMap(values, baseUnary(iteratee));
      }
      if (comparator) {
        includes = arrayIncludesWith;
        isCommon = false;
      }
      else if (values.length >= LARGE_ARRAY_SIZE) {
        includes = cacheHas;
        isCommon = false;
        values = new SetCache(values);
      }
      outer:
      while (++index < length) {
        var value = array[index],
            computed = iteratee ? iteratee(value) : value;

        value = (comparator || value !== 0) ? value : 0;
        if (isCommon && computed === computed) {
          var valuesIndex = valuesLength;
          while (valuesIndex--) {
            if (values[valuesIndex] === computed) {
              continue outer;
            }
          }
          result.push(value);
        }
        else if (!includes(values, computed, comparator)) {
          result.push(value);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.forEach` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     */
    var baseEach = createBaseEach(baseForOwn);

    /**
     * The base implementation of `_.forEachRight` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     */
    var baseEachRight = createBaseEach(baseForOwnRight, true);

    /**
     * The base implementation of `_.every` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {boolean} Returns `true` if all elements pass the predicate check,
     *  else `false`
     */
    function baseEvery(collection, predicate) {
      var result = true;
      baseEach(collection, function(value, index, collection) {
        result = !!predicate(value, index, collection);
        return result;
      });
      return result;
    }

    /**
     * The base implementation of methods like `_.max` and `_.min` which accepts a
     * `comparator` to determine the extremum value.
     *
     * @private
     * @param {Array} array The array to iterate over.
     * @param {Function} iteratee The iteratee invoked per iteration.
     * @param {Function} comparator The comparator used to compare values.
     * @returns {*} Returns the extremum value.
     */
    function baseExtremum(array, iteratee, comparator) {
      var index = -1,
          length = array.length;

      while (++index < length) {
        var value = array[index],
            current = iteratee(value);

        if (current != null && (computed === undefined
              ? (current === current && !isSymbol(current))
              : comparator(current, computed)
            )) {
          var computed = current,
              result = value;
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.fill` without an iteratee call guard.
     *
     * @private
     * @param {Array} array The array to fill.
     * @param {*} value The value to fill `array` with.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns `array`.
     */
    function baseFill(array, value, start, end) {
      var length = array.length;

      start = toInteger(start);
      if (start < 0) {
        start = -start > length ? 0 : (length + start);
      }
      end = (end === undefined || end > length) ? length : toInteger(end);
      if (end < 0) {
        end += length;
      }
      end = start > end ? 0 : toLength(end);
      while (start < end) {
        array[start++] = value;
      }
      return array;
    }

    /**
     * The base implementation of `_.filter` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {Array} Returns the new filtered array.
     */
    function baseFilter(collection, predicate) {
      var result = [];
      baseEach(collection, function(value, index, collection) {
        if (predicate(value, index, collection)) {
          result.push(value);
        }
      });
      return result;
    }

    /**
     * The base implementation of `_.flatten` with support for restricting flattening.
     *
     * @private
     * @param {Array} array The array to flatten.
     * @param {number} depth The maximum recursion depth.
     * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
     * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
     * @param {Array} [result=[]] The initial result value.
     * @returns {Array} Returns the new flattened array.
     */
    function baseFlatten(array, depth, predicate, isStrict, result) {
      var index = -1,
          length = array.length;

      predicate || (predicate = isFlattenable);
      result || (result = []);

      while (++index < length) {
        var value = array[index];
        if (depth > 0 && predicate(value)) {
          if (depth > 1) {
            // Recursively flatten arrays (susceptible to call stack limits).
            baseFlatten(value, depth - 1, predicate, isStrict, result);
          } else {
            arrayPush(result, value);
          }
        } else if (!isStrict) {
          result[result.length] = value;
        }
      }
      return result;
    }

    /**
     * The base implementation of `baseForOwn` which iterates over `object`
     * properties returned by `keysFunc` and invokes `iteratee` for each property.
     * Iteratee functions may exit iteration early by explicitly returning `false`.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @param {Function} keysFunc The function to get the keys of `object`.
     * @returns {Object} Returns `object`.
     */
    var baseFor = createBaseFor();

    /**
     * This function is like `baseFor` except that it iterates over properties
     * in the opposite order.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @param {Function} keysFunc The function to get the keys of `object`.
     * @returns {Object} Returns `object`.
     */
    var baseForRight = createBaseFor(true);

    /**
     * The base implementation of `_.forOwn` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Object} Returns `object`.
     */
    function baseForOwn(object, iteratee) {
      return object && baseFor(object, iteratee, keys);
    }

    /**
     * The base implementation of `_.forOwnRight` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Object} Returns `object`.
     */
    function baseForOwnRight(object, iteratee) {
      return object && baseForRight(object, iteratee, keys);
    }

    /**
     * The base implementation of `_.functions` which creates an array of
     * `object` function property names filtered from `props`.
     *
     * @private
     * @param {Object} object The object to inspect.
     * @param {Array} props The property names to filter.
     * @returns {Array} Returns the function names.
     */
    function baseFunctions(object, props) {
      return arrayFilter(props, function(key) {
        return isFunction(object[key]);
      });
    }

    /**
     * The base implementation of `_.get` without support for default values.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to get.
     * @returns {*} Returns the resolved value.
     */
    function baseGet(object, path) {
      path = isKey(path, object) ? [path] : castPath(path);

      var index = 0,
          length = path.length;

      while (object != null && index < length) {
        object = object[toKey(path[index++])];
      }
      return (index && index == length) ? object : undefined;
    }

    /**
     * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
     * `keysFunc` and `symbolsFunc` to get the enumerable property names and
     * symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Function} keysFunc The function to get the keys of `object`.
     * @param {Function} symbolsFunc The function to get the symbols of `object`.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function baseGetAllKeys(object, keysFunc, symbolsFunc) {
      var result = keysFunc(object);
      return isArray(object) ? result : arrayPush(result, symbolsFunc(object));
    }

    /**
     * The base implementation of `_.gt` which doesn't coerce arguments to numbers.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is greater than `other`,
     *  else `false`.
     */
    function baseGt(value, other) {
      return value > other;
    }

    /**
     * The base implementation of `_.has` without support for deep paths.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} key The key to check.
     * @returns {boolean} Returns `true` if `key` exists, else `false`.
     */
    function baseHas(object, key) {
      // Avoid a bug in IE 10-11 where objects with a [[Prototype]] of `null`,
      // that are composed entirely of index properties, return `false` for
      // `hasOwnProperty` checks of them.
      return hasOwnProperty.call(object, key) ||
        (typeof object == 'object' && key in object && getPrototype(object) === null);
    }

    /**
     * The base implementation of `_.hasIn` without support for deep paths.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} key The key to check.
     * @returns {boolean} Returns `true` if `key` exists, else `false`.
     */
    function baseHasIn(object, key) {
      return key in Object(object);
    }

    /**
     * The base implementation of `_.inRange` which doesn't coerce arguments to numbers.
     *
     * @private
     * @param {number} number The number to check.
     * @param {number} start The start of the range.
     * @param {number} end The end of the range.
     * @returns {boolean} Returns `true` if `number` is in the range, else `false`.
     */
    function baseInRange(number, start, end) {
      return number >= nativeMin(start, end) && number < nativeMax(start, end);
    }

    /**
     * The base implementation of methods like `_.intersection`, without support
     * for iteratee shorthands, that accepts an array of arrays to inspect.
     *
     * @private
     * @param {Array} arrays The arrays to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of shared values.
     */
    function baseIntersection(arrays, iteratee, comparator) {
      var includes = comparator ? arrayIncludesWith : arrayIncludes,
          length = arrays[0].length,
          othLength = arrays.length,
          othIndex = othLength,
          caches = Array(othLength),
          maxLength = Infinity,
          result = [];

      while (othIndex--) {
        var array = arrays[othIndex];
        if (othIndex && iteratee) {
          array = arrayMap(array, baseUnary(iteratee));
        }
        maxLength = nativeMin(array.length, maxLength);
        caches[othIndex] = !comparator && (iteratee || (length >= 120 && array.length >= 120))
          ? new SetCache(othIndex && array)
          : undefined;
      }
      array = arrays[0];

      var index = -1,
          seen = caches[0];

      outer:
      while (++index < length && result.length < maxLength) {
        var value = array[index],
            computed = iteratee ? iteratee(value) : value;

        value = (comparator || value !== 0) ? value : 0;
        if (!(seen
              ? cacheHas(seen, computed)
              : includes(result, computed, comparator)
            )) {
          othIndex = othLength;
          while (--othIndex) {
            var cache = caches[othIndex];
            if (!(cache
                  ? cacheHas(cache, computed)
                  : includes(arrays[othIndex], computed, comparator))
                ) {
              continue outer;
            }
          }
          if (seen) {
            seen.push(computed);
          }
          result.push(value);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.invert` and `_.invertBy` which inverts
     * `object` with values transformed by `iteratee` and set by `setter`.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} setter The function to set `accumulator` values.
     * @param {Function} iteratee The iteratee to transform values.
     * @param {Object} accumulator The initial inverted object.
     * @returns {Function} Returns `accumulator`.
     */
    function baseInverter(object, setter, iteratee, accumulator) {
      baseForOwn(object, function(value, key, object) {
        setter(accumulator, iteratee(value), key, object);
      });
      return accumulator;
    }

    /**
     * The base implementation of `_.invoke` without support for individual
     * method arguments.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the method to invoke.
     * @param {Array} args The arguments to invoke the method with.
     * @returns {*} Returns the result of the invoked method.
     */
    function baseInvoke(object, path, args) {
      if (!isKey(path, object)) {
        path = castPath(path);
        object = parent(object, path);
        path = last(path);
      }
      var func = object == null ? object : object[toKey(path)];
      return func == null ? undefined : apply(func, object, args);
    }

    /**
     * The base implementation of `_.isEqual` which supports partial comparisons
     * and tracks traversed objects.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @param {Function} [customizer] The function to customize comparisons.
     * @param {boolean} [bitmask] The bitmask of comparison flags.
     *  The bitmask may be composed of the following flags:
     *     1 - Unordered comparison
     *     2 - Partial comparison
     * @param {Object} [stack] Tracks traversed `value` and `other` objects.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     */
    function baseIsEqual(value, other, customizer, bitmask, stack) {
      if (value === other) {
        return true;
      }
      if (value == null || other == null || (!isObject(value) && !isObjectLike(other))) {
        return value !== value && other !== other;
      }
      return baseIsEqualDeep(value, other, baseIsEqual, customizer, bitmask, stack);
    }

    /**
     * A specialized version of `baseIsEqual` for arrays and objects which performs
     * deep comparisons and tracks traversed objects enabling objects with circular
     * references to be compared.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Function} [customizer] The function to customize comparisons.
     * @param {number} [bitmask] The bitmask of comparison flags. See `baseIsEqual`
     *  for more details.
     * @param {Object} [stack] Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function baseIsEqualDeep(object, other, equalFunc, customizer, bitmask, stack) {
      var objIsArr = isArray(object),
          othIsArr = isArray(other),
          objTag = arrayTag,
          othTag = arrayTag;

      if (!objIsArr) {
        objTag = getTag(object);
        objTag = objTag == argsTag ? objectTag : objTag;
      }
      if (!othIsArr) {
        othTag = getTag(other);
        othTag = othTag == argsTag ? objectTag : othTag;
      }
      var objIsObj = objTag == objectTag && !isHostObject(object),
          othIsObj = othTag == objectTag && !isHostObject(other),
          isSameTag = objTag == othTag;

      if (isSameTag && !objIsObj) {
        stack || (stack = new Stack);
        return (objIsArr || isTypedArray(object))
          ? equalArrays(object, other, equalFunc, customizer, bitmask, stack)
          : equalByTag(object, other, objTag, equalFunc, customizer, bitmask, stack);
      }
      if (!(bitmask & PARTIAL_COMPARE_FLAG)) {
        var objIsWrapped = objIsObj && hasOwnProperty.call(object, '__wrapped__'),
            othIsWrapped = othIsObj && hasOwnProperty.call(other, '__wrapped__');

        if (objIsWrapped || othIsWrapped) {
          var objUnwrapped = objIsWrapped ? object.value() : object,
              othUnwrapped = othIsWrapped ? other.value() : other;

          stack || (stack = new Stack);
          return equalFunc(objUnwrapped, othUnwrapped, customizer, bitmask, stack);
        }
      }
      if (!isSameTag) {
        return false;
      }
      stack || (stack = new Stack);
      return equalObjects(object, other, equalFunc, customizer, bitmask, stack);
    }

    /**
     * The base implementation of `_.isMatch` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property values to match.
     * @param {Array} matchData The property names, values, and compare flags to match.
     * @param {Function} [customizer] The function to customize comparisons.
     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
     */
    function baseIsMatch(object, source, matchData, customizer) {
      var index = matchData.length,
          length = index,
          noCustomizer = !customizer;

      if (object == null) {
        return !length;
      }
      object = Object(object);
      while (index--) {
        var data = matchData[index];
        if ((noCustomizer && data[2])
              ? data[1] !== object[data[0]]
              : !(data[0] in object)
            ) {
          return false;
        }
      }
      while (++index < length) {
        data = matchData[index];
        var key = data[0],
            objValue = object[key],
            srcValue = data[1];

        if (noCustomizer && data[2]) {
          if (objValue === undefined && !(key in object)) {
            return false;
          }
        } else {
          var stack = new Stack;
          if (customizer) {
            var result = customizer(objValue, srcValue, key, object, source, stack);
          }
          if (!(result === undefined
                ? baseIsEqual(srcValue, objValue, customizer, UNORDERED_COMPARE_FLAG | PARTIAL_COMPARE_FLAG, stack)
                : result
              )) {
            return false;
          }
        }
      }
      return true;
    }

    /**
     * The base implementation of `_.iteratee`.
     *
     * @private
     * @param {*} [value=_.identity] The value to convert to an iteratee.
     * @returns {Function} Returns the iteratee.
     */
    function baseIteratee(value) {
      // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
      // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
      if (typeof value == 'function') {
        return value;
      }
      if (value == null) {
        return identity;
      }
      if (typeof value == 'object') {
        return isArray(value)
          ? baseMatchesProperty(value[0], value[1])
          : baseMatches(value);
      }
      return property(value);
    }

    /**
     * The base implementation of `_.keys` which doesn't skip the constructor
     * property of prototypes or treat sparse arrays as dense.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     */
    function baseKeys(object) {
      return nativeKeys(Object(object));
    }

    /**
     * The base implementation of `_.keysIn` which doesn't skip the constructor
     * property of prototypes or treat sparse arrays as dense.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     */
    function baseKeysIn(object) {
      object = object == null ? object : Object(object);

      var result = [];
      for (var key in object) {
        result.push(key);
      }
      return result;
    }

    // Fallback for IE < 9 with es6-shim.
    if (enumerate && !propertyIsEnumerable.call({ 'valueOf': 1 }, 'valueOf')) {
      baseKeysIn = function(object) {
        return iteratorToArray(enumerate(object));
      };
    }

    /**
     * The base implementation of `_.lt` which doesn't coerce arguments to numbers.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is less than `other`,
     *  else `false`.
     */
    function baseLt(value, other) {
      return value < other;
    }

    /**
     * The base implementation of `_.map` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Array} Returns the new mapped array.
     */
    function baseMap(collection, iteratee) {
      var index = -1,
          result = isArrayLike(collection) ? Array(collection.length) : [];

      baseEach(collection, function(value, key, collection) {
        result[++index] = iteratee(value, key, collection);
      });
      return result;
    }

    /**
     * The base implementation of `_.matches` which doesn't clone `source`.
     *
     * @private
     * @param {Object} source The object of property values to match.
     * @returns {Function} Returns the new spec function.
     */
    function baseMatches(source) {
      var matchData = getMatchData(source);
      if (matchData.length == 1 && matchData[0][2]) {
        return matchesStrictComparable(matchData[0][0], matchData[0][1]);
      }
      return function(object) {
        return object === source || baseIsMatch(object, source, matchData);
      };
    }

    /**
     * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
     *
     * @private
     * @param {string} path The path of the property to get.
     * @param {*} srcValue The value to match.
     * @returns {Function} Returns the new spec function.
     */
    function baseMatchesProperty(path, srcValue) {
      if (isKey(path) && isStrictComparable(srcValue)) {
        return matchesStrictComparable(toKey(path), srcValue);
      }
      return function(object) {
        var objValue = get(object, path);
        return (objValue === undefined && objValue === srcValue)
          ? hasIn(object, path)
          : baseIsEqual(srcValue, objValue, undefined, UNORDERED_COMPARE_FLAG | PARTIAL_COMPARE_FLAG);
      };
    }

    /**
     * The base implementation of `_.merge` without support for multiple sources.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @param {number} srcIndex The index of `source`.
     * @param {Function} [customizer] The function to customize merged values.
     * @param {Object} [stack] Tracks traversed source values and their merged
     *  counterparts.
     */
    function baseMerge(object, source, srcIndex, customizer, stack) {
      if (object === source) {
        return;
      }
      if (!(isArray(source) || isTypedArray(source))) {
        var props = keysIn(source);
      }
      arrayEach(props || source, function(srcValue, key) {
        if (props) {
          key = srcValue;
          srcValue = source[key];
        }
        if (isObject(srcValue)) {
          stack || (stack = new Stack);
          baseMergeDeep(object, source, key, srcIndex, baseMerge, customizer, stack);
        }
        else {
          var newValue = customizer
            ? customizer(object[key], srcValue, (key + ''), object, source, stack)
            : undefined;

          if (newValue === undefined) {
            newValue = srcValue;
          }
          assignMergeValue(object, key, newValue);
        }
      });
    }

    /**
     * A specialized version of `baseMerge` for arrays and objects which performs
     * deep merges and tracks traversed objects enabling objects with circular
     * references to be merged.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @param {string} key The key of the value to merge.
     * @param {number} srcIndex The index of `source`.
     * @param {Function} mergeFunc The function to merge values.
     * @param {Function} [customizer] The function to customize assigned values.
     * @param {Object} [stack] Tracks traversed source values and their merged
     *  counterparts.
     */
    function baseMergeDeep(object, source, key, srcIndex, mergeFunc, customizer, stack) {
      var objValue = object[key],
          srcValue = source[key],
          stacked = stack.get(srcValue);

      if (stacked) {
        assignMergeValue(object, key, stacked);
        return;
      }
      var newValue = customizer
        ? customizer(objValue, srcValue, (key + ''), object, source, stack)
        : undefined;

      var isCommon = newValue === undefined;

      if (isCommon) {
        newValue = srcValue;
        if (isArray(srcValue) || isTypedArray(srcValue)) {
          if (isArray(objValue)) {
            newValue = objValue;
          }
          else if (isArrayLikeObject(objValue)) {
            newValue = copyArray(objValue);
          }
          else {
            isCommon = false;
            newValue = baseClone(srcValue, true);
          }
        }
        else if (isPlainObject(srcValue) || isArguments(srcValue)) {
          if (isArguments(objValue)) {
            newValue = toPlainObject(objValue);
          }
          else if (!isObject(objValue) || (srcIndex && isFunction(objValue))) {
            isCommon = false;
            newValue = baseClone(srcValue, true);
          }
          else {
            newValue = objValue;
          }
        }
        else {
          isCommon = false;
        }
      }
      stack.set(srcValue, newValue);

      if (isCommon) {
        // Recursively merge objects and arrays (susceptible to call stack limits).
        mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
      }
      stack['delete'](srcValue);
      assignMergeValue(object, key, newValue);
    }

    /**
     * The base implementation of `_.nth` which doesn't coerce `n` to an integer.
     *
     * @private
     * @param {Array} array The array to query.
     * @param {number} n The index of the element to return.
     * @returns {*} Returns the nth element of `array`.
     */
    function baseNth(array, n) {
      var length = array.length;
      if (!length) {
        return;
      }
      n += n < 0 ? length : 0;
      return isIndex(n, length) ? array[n] : undefined;
    }

    /**
     * The base implementation of `_.orderBy` without param guards.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function[]|Object[]|string[]} iteratees The iteratees to sort by.
     * @param {string[]} orders The sort orders of `iteratees`.
     * @returns {Array} Returns the new sorted array.
     */
    function baseOrderBy(collection, iteratees, orders) {
      var index = -1;
      iteratees = arrayMap(iteratees.length ? iteratees : [identity], baseUnary(getIteratee()));

      var result = baseMap(collection, function(value, key, collection) {
        var criteria = arrayMap(iteratees, function(iteratee) {
          return iteratee(value);
        });
        return { 'criteria': criteria, 'index': ++index, 'value': value };
      });

      return baseSortBy(result, function(object, other) {
        return compareMultiple(object, other, orders);
      });
    }

    /**
     * The base implementation of `_.pick` without support for individual
     * property identifiers.
     *
     * @private
     * @param {Object} object The source object.
     * @param {string[]} props The property identifiers to pick.
     * @returns {Object} Returns the new object.
     */
    function basePick(object, props) {
      object = Object(object);
      return arrayReduce(props, function(result, key) {
        if (key in object) {
          result[key] = object[key];
        }
        return result;
      }, {});
    }

    /**
     * The base implementation of  `_.pickBy` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The source object.
     * @param {Function} predicate The function invoked per property.
     * @returns {Object} Returns the new object.
     */
    function basePickBy(object, predicate) {
      var index = -1,
          props = getAllKeysIn(object),
          length = props.length,
          result = {};

      while (++index < length) {
        var key = props[index],
            value = object[key];

        if (predicate(value, key)) {
          result[key] = value;
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.property` without support for deep paths.
     *
     * @private
     * @param {string} key The key of the property to get.
     * @returns {Function} Returns the new accessor function.
     */
    function baseProperty(key) {
      return function(object) {
        return object == null ? undefined : object[key];
      };
    }

    /**
     * A specialized version of `baseProperty` which supports deep paths.
     *
     * @private
     * @param {Array|string} path The path of the property to get.
     * @returns {Function} Returns the new accessor function.
     */
    function basePropertyDeep(path) {
      return function(object) {
        return baseGet(object, path);
      };
    }

    /**
     * The base implementation of `_.pullAllBy` without support for iteratee
     * shorthands.
     *
     * @private
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns `array`.
     */
    function basePullAll(array, values, iteratee, comparator) {
      var indexOf = comparator ? baseIndexOfWith : baseIndexOf,
          index = -1,
          length = values.length,
          seen = array;

      if (iteratee) {
        seen = arrayMap(array, baseUnary(iteratee));
      }
      while (++index < length) {
        var fromIndex = 0,
            value = values[index],
            computed = iteratee ? iteratee(value) : value;

        while ((fromIndex = indexOf(seen, computed, fromIndex, comparator)) > -1) {
          if (seen !== array) {
            splice.call(seen, fromIndex, 1);
          }
          splice.call(array, fromIndex, 1);
        }
      }
      return array;
    }

    /**
     * The base implementation of `_.pullAt` without support for individual
     * indexes or capturing the removed elements.
     *
     * @private
     * @param {Array} array The array to modify.
     * @param {number[]} indexes The indexes of elements to remove.
     * @returns {Array} Returns `array`.
     */
    function basePullAt(array, indexes) {
      var length = array ? indexes.length : 0,
          lastIndex = length - 1;

      while (length--) {
        var index = indexes[length];
        if (length == lastIndex || index !== previous) {
          var previous = index;
          if (isIndex(index)) {
            splice.call(array, index, 1);
          }
          else if (!isKey(index, array)) {
            var path = castPath(index),
                object = parent(array, path);

            if (object != null) {
              delete object[toKey(last(path))];
            }
          }
          else {
            delete array[toKey(index)];
          }
        }
      }
      return array;
    }

    /**
     * The base implementation of `_.random` without support for returning
     * floating-point numbers.
     *
     * @private
     * @param {number} lower The lower bound.
     * @param {number} upper The upper bound.
     * @returns {number} Returns the random number.
     */
    function baseRandom(lower, upper) {
      return lower + nativeFloor(nativeRandom() * (upper - lower + 1));
    }

    /**
     * The base implementation of `_.range` and `_.rangeRight` which doesn't
     * coerce arguments to numbers.
     *
     * @private
     * @param {number} start The start of the range.
     * @param {number} end The end of the range.
     * @param {number} step The value to increment or decrement by.
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Array} Returns the range of numbers.
     */
    function baseRange(start, end, step, fromRight) {
      var index = -1,
          length = nativeMax(nativeCeil((end - start) / (step || 1)), 0),
          result = Array(length);

      while (length--) {
        result[fromRight ? length : ++index] = start;
        start += step;
      }
      return result;
    }

    /**
     * The base implementation of `_.repeat` which doesn't coerce arguments.
     *
     * @private
     * @param {string} string The string to repeat.
     * @param {number} n The number of times to repeat the string.
     * @returns {string} Returns the repeated string.
     */
    function baseRepeat(string, n) {
      var result = '';
      if (!string || n < 1 || n > MAX_SAFE_INTEGER) {
        return result;
      }
      // Leverage the exponentiation by squaring algorithm for a faster repeat.
      // See https://en.wikipedia.org/wiki/Exponentiation_by_squaring for more details.
      do {
        if (n % 2) {
          result += string;
        }
        n = nativeFloor(n / 2);
        if (n) {
          string += string;
        }
      } while (n);

      return result;
    }

    /**
     * The base implementation of `_.set`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to set.
     * @param {*} value The value to set.
     * @param {Function} [customizer] The function to customize path creation.
     * @returns {Object} Returns `object`.
     */
    function baseSet(object, path, value, customizer) {
      path = isKey(path, object) ? [path] : castPath(path);

      var index = -1,
          length = path.length,
          lastIndex = length - 1,
          nested = object;

      while (nested != null && ++index < length) {
        var key = toKey(path[index]);
        if (isObject(nested)) {
          var newValue = value;
          if (index != lastIndex) {
            var objValue = nested[key];
            newValue = customizer ? customizer(objValue, key, nested) : undefined;
            if (newValue === undefined) {
              newValue = objValue == null
                ? (isIndex(path[index + 1]) ? [] : {})
                : objValue;
            }
          }
          assignValue(nested, key, newValue);
        }
        nested = nested[key];
      }
      return object;
    }

    /**
     * The base implementation of `setData` without support for hot loop detection.
     *
     * @private
     * @param {Function} func The function to associate metadata with.
     * @param {*} data The metadata.
     * @returns {Function} Returns `func`.
     */
    var baseSetData = !metaMap ? identity : function(func, data) {
      metaMap.set(func, data);
      return func;
    };

    /**
     * The base implementation of `_.slice` without an iteratee call guard.
     *
     * @private
     * @param {Array} array The array to slice.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns the slice of `array`.
     */
    function baseSlice(array, start, end) {
      var index = -1,
          length = array.length;

      if (start < 0) {
        start = -start > length ? 0 : (length + start);
      }
      end = end > length ? length : end;
      if (end < 0) {
        end += length;
      }
      length = start > end ? 0 : ((end - start) >>> 0);
      start >>>= 0;

      var result = Array(length);
      while (++index < length) {
        result[index] = array[index + start];
      }
      return result;
    }

    /**
     * The base implementation of `_.some` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {boolean} Returns `true` if any element passes the predicate check,
     *  else `false`.
     */
    function baseSome(collection, predicate) {
      var result;

      baseEach(collection, function(value, index, collection) {
        result = predicate(value, index, collection);
        return !result;
      });
      return !!result;
    }

    /**
     * The base implementation of `_.sortedIndex` and `_.sortedLastIndex` which
     * performs a binary search of `array` to determine the index at which `value`
     * should be inserted into `array` in order to maintain its sort order.
     *
     * @private
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {boolean} [retHighest] Specify returning the highest qualified index.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     */
    function baseSortedIndex(array, value, retHighest) {
      var low = 0,
          high = array ? array.length : low;

      if (typeof value == 'number' && value === value && high <= HALF_MAX_ARRAY_LENGTH) {
        while (low < high) {
          var mid = (low + high) >>> 1,
              computed = array[mid];

          if (computed !== null && !isSymbol(computed) &&
              (retHighest ? (computed <= value) : (computed < value))) {
            low = mid + 1;
          } else {
            high = mid;
          }
        }
        return high;
      }
      return baseSortedIndexBy(array, value, identity, retHighest);
    }

    /**
     * The base implementation of `_.sortedIndexBy` and `_.sortedLastIndexBy`
     * which invokes `iteratee` for `value` and each element of `array` to compute
     * their sort ranking. The iteratee is invoked with one argument; (value).
     *
     * @private
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {Function} iteratee The iteratee invoked per element.
     * @param {boolean} [retHighest] Specify returning the highest qualified index.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     */
    function baseSortedIndexBy(array, value, iteratee, retHighest) {
      value = iteratee(value);

      var low = 0,
          high = array ? array.length : 0,
          valIsNaN = value !== value,
          valIsNull = value === null,
          valIsSymbol = isSymbol(value),
          valIsUndefined = value === undefined;

      while (low < high) {
        var mid = nativeFloor((low + high) / 2),
            computed = iteratee(array[mid]),
            othIsDefined = computed !== undefined,
            othIsNull = computed === null,
            othIsReflexive = computed === computed,
            othIsSymbol = isSymbol(computed);

        if (valIsNaN) {
          var setLow = retHighest || othIsReflexive;
        } else if (valIsUndefined) {
          setLow = othIsReflexive && (retHighest || othIsDefined);
        } else if (valIsNull) {
          setLow = othIsReflexive && othIsDefined && (retHighest || !othIsNull);
        } else if (valIsSymbol) {
          setLow = othIsReflexive && othIsDefined && !othIsNull && (retHighest || !othIsSymbol);
        } else if (othIsNull || othIsSymbol) {
          setLow = false;
        } else {
          setLow = retHighest ? (computed <= value) : (computed < value);
        }
        if (setLow) {
          low = mid + 1;
        } else {
          high = mid;
        }
      }
      return nativeMin(high, MAX_ARRAY_INDEX);
    }

    /**
     * The base implementation of `_.sortedUniq` and `_.sortedUniqBy` without
     * support for iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     */
    function baseSortedUniq(array, iteratee) {
      var index = -1,
          length = array.length,
          resIndex = 0,
          result = [];

      while (++index < length) {
        var value = array[index],
            computed = iteratee ? iteratee(value) : value;

        if (!index || !eq(computed, seen)) {
          var seen = computed;
          result[resIndex++] = value === 0 ? 0 : value;
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.toNumber` which doesn't ensure correct
     * conversions of binary, hexadecimal, or octal string values.
     *
     * @private
     * @param {*} value The value to process.
     * @returns {number} Returns the number.
     */
    function baseToNumber(value) {
      if (typeof value == 'number') {
        return value;
      }
      if (isSymbol(value)) {
        return NAN;
      }
      return +value;
    }

    /**
     * The base implementation of `_.toString` which doesn't convert nullish
     * values to empty strings.
     *
     * @private
     * @param {*} value The value to process.
     * @returns {string} Returns the string.
     */
    function baseToString(value) {
      // Exit early for strings to avoid a performance hit in some environments.
      if (typeof value == 'string') {
        return value;
      }
      if (isSymbol(value)) {
        return symbolToString ? symbolToString.call(value) : '';
      }
      var result = (value + '');
      return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
    }

    /**
     * The base implementation of `_.uniqBy` without support for iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     */
    function baseUniq(array, iteratee, comparator) {
      var index = -1,
          includes = arrayIncludes,
          length = array.length,
          isCommon = true,
          result = [],
          seen = result;

      if (comparator) {
        isCommon = false;
        includes = arrayIncludesWith;
      }
      else if (length >= LARGE_ARRAY_SIZE) {
        var set = iteratee ? null : createSet(array);
        if (set) {
          return setToArray(set);
        }
        isCommon = false;
        includes = cacheHas;
        seen = new SetCache;
      }
      else {
        seen = iteratee ? [] : result;
      }
      outer:
      while (++index < length) {
        var value = array[index],
            computed = iteratee ? iteratee(value) : value;

        value = (comparator || value !== 0) ? value : 0;
        if (isCommon && computed === computed) {
          var seenIndex = seen.length;
          while (seenIndex--) {
            if (seen[seenIndex] === computed) {
              continue outer;
            }
          }
          if (iteratee) {
            seen.push(computed);
          }
          result.push(value);
        }
        else if (!includes(seen, computed, comparator)) {
          if (seen !== result) {
            seen.push(computed);
          }
          result.push(value);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.unset`.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to unset.
     * @returns {boolean} Returns `true` if the property is deleted, else `false`.
     */
    function baseUnset(object, path) {
      path = isKey(path, object) ? [path] : castPath(path);
      object = parent(object, path);

      var key = toKey(last(path));
      return !(object != null && baseHas(object, key)) || delete object[key];
    }

    /**
     * The base implementation of `_.update`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to update.
     * @param {Function} updater The function to produce the updated value.
     * @param {Function} [customizer] The function to customize path creation.
     * @returns {Object} Returns `object`.
     */
    function baseUpdate(object, path, updater, customizer) {
      return baseSet(object, path, updater(baseGet(object, path)), customizer);
    }

    /**
     * The base implementation of methods like `_.dropWhile` and `_.takeWhile`
     * without support for iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to query.
     * @param {Function} predicate The function invoked per iteration.
     * @param {boolean} [isDrop] Specify dropping elements instead of taking them.
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Array} Returns the slice of `array`.
     */
    function baseWhile(array, predicate, isDrop, fromRight) {
      var length = array.length,
          index = fromRight ? length : -1;

      while ((fromRight ? index-- : ++index < length) &&
        predicate(array[index], index, array)) {}

      return isDrop
        ? baseSlice(array, (fromRight ? 0 : index), (fromRight ? index + 1 : length))
        : baseSlice(array, (fromRight ? index + 1 : 0), (fromRight ? length : index));
    }

    /**
     * The base implementation of `wrapperValue` which returns the result of
     * performing a sequence of actions on the unwrapped `value`, where each
     * successive action is supplied the return value of the previous.
     *
     * @private
     * @param {*} value The unwrapped value.
     * @param {Array} actions Actions to perform to resolve the unwrapped value.
     * @returns {*} Returns the resolved value.
     */
    function baseWrapperValue(value, actions) {
      var result = value;
      if (result instanceof LazyWrapper) {
        result = result.value();
      }
      return arrayReduce(actions, function(result, action) {
        return action.func.apply(action.thisArg, arrayPush([result], action.args));
      }, result);
    }

    /**
     * The base implementation of methods like `_.xor`, without support for
     * iteratee shorthands, that accepts an array of arrays to inspect.
     *
     * @private
     * @param {Array} arrays The arrays to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of values.
     */
    function baseXor(arrays, iteratee, comparator) {
      var index = -1,
          length = arrays.length;

      while (++index < length) {
        var result = result
          ? arrayPush(
              baseDifference(result, arrays[index], iteratee, comparator),
              baseDifference(arrays[index], result, iteratee, comparator)
            )
          : arrays[index];
      }
      return (result && result.length) ? baseUniq(result, iteratee, comparator) : [];
    }

    /**
     * This base implementation of `_.zipObject` which assigns values using `assignFunc`.
     *
     * @private
     * @param {Array} props The property identifiers.
     * @param {Array} values The property values.
     * @param {Function} assignFunc The function to assign values.
     * @returns {Object} Returns the new object.
     */
    function baseZipObject(props, values, assignFunc) {
      var index = -1,
          length = props.length,
          valsLength = values.length,
          result = {};

      while (++index < length) {
        var value = index < valsLength ? values[index] : undefined;
        assignFunc(result, props[index], value);
      }
      return result;
    }

    /**
     * Casts `value` to an empty array if it's not an array like object.
     *
     * @private
     * @param {*} value The value to inspect.
     * @returns {Array|Object} Returns the cast array-like object.
     */
    function castArrayLikeObject(value) {
      return isArrayLikeObject(value) ? value : [];
    }

    /**
     * Casts `value` to `identity` if it's not a function.
     *
     * @private
     * @param {*} value The value to inspect.
     * @returns {Function} Returns cast function.
     */
    function castFunction(value) {
      return typeof value == 'function' ? value : identity;
    }

    /**
     * Casts `value` to a path array if it's not one.
     *
     * @private
     * @param {*} value The value to inspect.
     * @returns {Array} Returns the cast property path array.
     */
    function castPath(value) {
      return isArray(value) ? value : stringToPath(value);
    }

    /**
     * Casts `array` to a slice if it's needed.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {number} start The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns the cast slice.
     */
    function castSlice(array, start, end) {
      var length = array.length;
      end = end === undefined ? length : end;
      return (!start && end >= length) ? array : baseSlice(array, start, end);
    }

    /**
     * Creates a clone of  `buffer`.
     *
     * @private
     * @param {Buffer} buffer The buffer to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Buffer} Returns the cloned buffer.
     */
    function cloneBuffer(buffer, isDeep) {
      if (isDeep) {
        return buffer.slice();
      }
      var result = new buffer.constructor(buffer.length);
      buffer.copy(result);
      return result;
    }

    /**
     * Creates a clone of `arrayBuffer`.
     *
     * @private
     * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
     * @returns {ArrayBuffer} Returns the cloned array buffer.
     */
    function cloneArrayBuffer(arrayBuffer) {
      var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
      new Uint8Array(result).set(new Uint8Array(arrayBuffer));
      return result;
    }

    /**
     * Creates a clone of `dataView`.
     *
     * @private
     * @param {Object} dataView The data view to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the cloned data view.
     */
    function cloneDataView(dataView, isDeep) {
      var buffer = isDeep ? cloneArrayBuffer(dataView.buffer) : dataView.buffer;
      return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
    }

    /**
     * Creates a clone of `map`.
     *
     * @private
     * @param {Object} map The map to clone.
     * @param {Function} cloneFunc The function to clone values.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the cloned map.
     */
    function cloneMap(map, isDeep, cloneFunc) {
      var array = isDeep ? cloneFunc(mapToArray(map), true) : mapToArray(map);
      return arrayReduce(array, addMapEntry, new map.constructor);
    }

    /**
     * Creates a clone of `regexp`.
     *
     * @private
     * @param {Object} regexp The regexp to clone.
     * @returns {Object} Returns the cloned regexp.
     */
    function cloneRegExp(regexp) {
      var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
      result.lastIndex = regexp.lastIndex;
      return result;
    }

    /**
     * Creates a clone of `set`.
     *
     * @private
     * @param {Object} set The set to clone.
     * @param {Function} cloneFunc The function to clone values.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the cloned set.
     */
    function cloneSet(set, isDeep, cloneFunc) {
      var array = isDeep ? cloneFunc(setToArray(set), true) : setToArray(set);
      return arrayReduce(array, addSetEntry, new set.constructor);
    }

    /**
     * Creates a clone of the `symbol` object.
     *
     * @private
     * @param {Object} symbol The symbol object to clone.
     * @returns {Object} Returns the cloned symbol object.
     */
    function cloneSymbol(symbol) {
      return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
    }

    /**
     * Creates a clone of `typedArray`.
     *
     * @private
     * @param {Object} typedArray The typed array to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the cloned typed array.
     */
    function cloneTypedArray(typedArray, isDeep) {
      var buffer = isDeep ? cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
      return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
    }

    /**
     * Compares values to sort them in ascending order.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {number} Returns the sort order indicator for `value`.
     */
    function compareAscending(value, other) {
      if (value !== other) {
        var valIsDefined = value !== undefined,
            valIsNull = value === null,
            valIsReflexive = value === value,
            valIsSymbol = isSymbol(value);

        var othIsDefined = other !== undefined,
            othIsNull = other === null,
            othIsReflexive = other === other,
            othIsSymbol = isSymbol(other);

        if ((!othIsNull && !othIsSymbol && !valIsSymbol && value > other) ||
            (valIsSymbol && othIsDefined && othIsReflexive && !othIsNull && !othIsSymbol) ||
            (valIsNull && othIsDefined && othIsReflexive) ||
            (!valIsDefined && othIsReflexive) ||
            !valIsReflexive) {
          return 1;
        }
        if ((!valIsNull && !valIsSymbol && !othIsSymbol && value < other) ||
            (othIsSymbol && valIsDefined && valIsReflexive && !valIsNull && !valIsSymbol) ||
            (othIsNull && valIsDefined && valIsReflexive) ||
            (!othIsDefined && valIsReflexive) ||
            !othIsReflexive) {
          return -1;
        }
      }
      return 0;
    }

    /**
     * Used by `_.orderBy` to compare multiple properties of a value to another
     * and stable sort them.
     *
     * If `orders` is unspecified, all values are sorted in ascending order. Otherwise,
     * specify an order of "desc" for descending or "asc" for ascending sort order
     * of corresponding values.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {boolean[]|string[]} orders The order to sort by for each property.
     * @returns {number} Returns the sort order indicator for `object`.
     */
    function compareMultiple(object, other, orders) {
      var index = -1,
          objCriteria = object.criteria,
          othCriteria = other.criteria,
          length = objCriteria.length,
          ordersLength = orders.length;

      while (++index < length) {
        var result = compareAscending(objCriteria[index], othCriteria[index]);
        if (result) {
          if (index >= ordersLength) {
            return result;
          }
          var order = orders[index];
          return result * (order == 'desc' ? -1 : 1);
        }
      }
      // Fixes an `Array#sort` bug in the JS engine embedded in Adobe applications
      // that causes it, under certain circumstances, to provide the same value for
      // `object` and `other`. See https://github.com/jashkenas/underscore/pull/1247
      // for more details.
      //
      // This also ensures a stable sort in V8 and other engines.
      // See https://bugs.chromium.org/p/v8/issues/detail?id=90 for more details.
      return object.index - other.index;
    }

    /**
     * Creates an array that is the composition of partially applied arguments,
     * placeholders, and provided arguments into a single array of arguments.
     *
     * @private
     * @param {Array} args The provided arguments.
     * @param {Array} partials The arguments to prepend to those provided.
     * @param {Array} holders The `partials` placeholder indexes.
     * @params {boolean} [isCurried] Specify composing for a curried function.
     * @returns {Array} Returns the new array of composed arguments.
     */
    function composeArgs(args, partials, holders, isCurried) {
      var argsIndex = -1,
          argsLength = args.length,
          holdersLength = holders.length,
          leftIndex = -1,
          leftLength = partials.length,
          rangeLength = nativeMax(argsLength - holdersLength, 0),
          result = Array(leftLength + rangeLength),
          isUncurried = !isCurried;

      while (++leftIndex < leftLength) {
        result[leftIndex] = partials[leftIndex];
      }
      while (++argsIndex < holdersLength) {
        if (isUncurried || argsIndex < argsLength) {
          result[holders[argsIndex]] = args[argsIndex];
        }
      }
      while (rangeLength--) {
        result[leftIndex++] = args[argsIndex++];
      }
      return result;
    }

    /**
     * This function is like `composeArgs` except that the arguments composition
     * is tailored for `_.partialRight`.
     *
     * @private
     * @param {Array} args The provided arguments.
     * @param {Array} partials The arguments to append to those provided.
     * @param {Array} holders The `partials` placeholder indexes.
     * @params {boolean} [isCurried] Specify composing for a curried function.
     * @returns {Array} Returns the new array of composed arguments.
     */
    function composeArgsRight(args, partials, holders, isCurried) {
      var argsIndex = -1,
          argsLength = args.length,
          holdersIndex = -1,
          holdersLength = holders.length,
          rightIndex = -1,
          rightLength = partials.length,
          rangeLength = nativeMax(argsLength - holdersLength, 0),
          result = Array(rangeLength + rightLength),
          isUncurried = !isCurried;

      while (++argsIndex < rangeLength) {
        result[argsIndex] = args[argsIndex];
      }
      var offset = argsIndex;
      while (++rightIndex < rightLength) {
        result[offset + rightIndex] = partials[rightIndex];
      }
      while (++holdersIndex < holdersLength) {
        if (isUncurried || argsIndex < argsLength) {
          result[offset + holders[holdersIndex]] = args[argsIndex++];
        }
      }
      return result;
    }

    /**
     * Copies the values of `source` to `array`.
     *
     * @private
     * @param {Array} source The array to copy values from.
     * @param {Array} [array=[]] The array to copy values to.
     * @returns {Array} Returns `array`.
     */
    function copyArray(source, array) {
      var index = -1,
          length = source.length;

      array || (array = Array(length));
      while (++index < length) {
        array[index] = source[index];
      }
      return array;
    }

    /**
     * Copies properties of `source` to `object`.
     *
     * @private
     * @param {Object} source The object to copy properties from.
     * @param {Array} props The property identifiers to copy.
     * @param {Object} [object={}] The object to copy properties to.
     * @param {Function} [customizer] The function to customize copied values.
     * @returns {Object} Returns `object`.
     */
    function copyObject(source, props, object, customizer) {
      object || (object = {});

      var index = -1,
          length = props.length;

      while (++index < length) {
        var key = props[index];

        var newValue = customizer
          ? customizer(object[key], source[key], key, object, source)
          : source[key];

        assignValue(object, key, newValue);
      }
      return object;
    }

    /**
     * Copies own symbol properties of `source` to `object`.
     *
     * @private
     * @param {Object} source The object to copy symbols from.
     * @param {Object} [object={}] The object to copy symbols to.
     * @returns {Object} Returns `object`.
     */
    function copySymbols(source, object) {
      return copyObject(source, getSymbols(source), object);
    }

    /**
     * Creates a function like `_.groupBy`.
     *
     * @private
     * @param {Function} setter The function to set accumulator values.
     * @param {Function} [initializer] The accumulator object initializer.
     * @returns {Function} Returns the new aggregator function.
     */
    function createAggregator(setter, initializer) {
      return function(collection, iteratee) {
        var func = isArray(collection) ? arrayAggregator : baseAggregator,
            accumulator = initializer ? initializer() : {};

        return func(collection, setter, getIteratee(iteratee), accumulator);
      };
    }

    /**
     * Creates a function like `_.assign`.
     *
     * @private
     * @param {Function} assigner The function to assign values.
     * @returns {Function} Returns the new assigner function.
     */
    function createAssigner(assigner) {
      return rest(function(object, sources) {
        var index = -1,
            length = sources.length,
            customizer = length > 1 ? sources[length - 1] : undefined,
            guard = length > 2 ? sources[2] : undefined;

        customizer = (assigner.length > 3 && typeof customizer == 'function')
          ? (length--, customizer)
          : undefined;

        if (guard && isIterateeCall(sources[0], sources[1], guard)) {
          customizer = length < 3 ? undefined : customizer;
          length = 1;
        }
        object = Object(object);
        while (++index < length) {
          var source = sources[index];
          if (source) {
            assigner(object, source, index, customizer);
          }
        }
        return object;
      });
    }

    /**
     * Creates a `baseEach` or `baseEachRight` function.
     *
     * @private
     * @param {Function} eachFunc The function to iterate over a collection.
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new base function.
     */
    function createBaseEach(eachFunc, fromRight) {
      return function(collection, iteratee) {
        if (collection == null) {
          return collection;
        }
        if (!isArrayLike(collection)) {
          return eachFunc(collection, iteratee);
        }
        var length = collection.length,
            index = fromRight ? length : -1,
            iterable = Object(collection);

        while ((fromRight ? index-- : ++index < length)) {
          if (iteratee(iterable[index], index, iterable) === false) {
            break;
          }
        }
        return collection;
      };
    }

    /**
     * Creates a base function for methods like `_.forIn` and `_.forOwn`.
     *
     * @private
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new base function.
     */
    function createBaseFor(fromRight) {
      return function(object, iteratee, keysFunc) {
        var index = -1,
            iterable = Object(object),
            props = keysFunc(object),
            length = props.length;

        while (length--) {
          var key = props[fromRight ? length : ++index];
          if (iteratee(iterable[key], key, iterable) === false) {
            break;
          }
        }
        return object;
      };
    }

    /**
     * Creates a function that wraps `func` to invoke it with the optional `this`
     * binding of `thisArg`.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask of wrapper flags. See `createWrapper`
     *  for more details.
     * @param {*} [thisArg] The `this` binding of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createBaseWrapper(func, bitmask, thisArg) {
      var isBind = bitmask & BIND_FLAG,
          Ctor = createCtorWrapper(func);

      function wrapper() {
        var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
        return fn.apply(isBind ? thisArg : this, arguments);
      }
      return wrapper;
    }

    /**
     * Creates a function like `_.lowerFirst`.
     *
     * @private
     * @param {string} methodName The name of the `String` case method to use.
     * @returns {Function} Returns the new case function.
     */
    function createCaseFirst(methodName) {
      return function(string) {
        string = toString(string);

        var strSymbols = reHasComplexSymbol.test(string)
          ? stringToArray(string)
          : undefined;

        var chr = strSymbols
          ? strSymbols[0]
          : string.charAt(0);

        var trailing = strSymbols
          ? castSlice(strSymbols, 1).join('')
          : string.slice(1);

        return chr[methodName]() + trailing;
      };
    }

    /**
     * Creates a function like `_.camelCase`.
     *
     * @private
     * @param {Function} callback The function to combine each word.
     * @returns {Function} Returns the new compounder function.
     */
    function createCompounder(callback) {
      return function(string) {
        return arrayReduce(words(deburr(string).replace(reApos, '')), callback, '');
      };
    }

    /**
     * Creates a function that produces an instance of `Ctor` regardless of
     * whether it was invoked as part of a `new` expression or by `call` or `apply`.
     *
     * @private
     * @param {Function} Ctor The constructor to wrap.
     * @returns {Function} Returns the new wrapped function.
     */
    function createCtorWrapper(Ctor) {
      return function() {
        // Use a `switch` statement to work with class constructors. See
        // http://ecma-international.org/ecma-262/6.0/#sec-ecmascript-function-objects-call-thisargument-argumentslist
        // for more details.
        var args = arguments;
        switch (args.length) {
          case 0: return new Ctor;
          case 1: return new Ctor(args[0]);
          case 2: return new Ctor(args[0], args[1]);
          case 3: return new Ctor(args[0], args[1], args[2]);
          case 4: return new Ctor(args[0], args[1], args[2], args[3]);
          case 5: return new Ctor(args[0], args[1], args[2], args[3], args[4]);
          case 6: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5]);
          case 7: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5], args[6]);
        }
        var thisBinding = baseCreate(Ctor.prototype),
            result = Ctor.apply(thisBinding, args);

        // Mimic the constructor's `return` behavior.
        // See https://es5.github.io/#x13.2.2 for more details.
        return isObject(result) ? result : thisBinding;
      };
    }

    /**
     * Creates a function that wraps `func` to enable currying.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask of wrapper flags. See `createWrapper`
     *  for more details.
     * @param {number} arity The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createCurryWrapper(func, bitmask, arity) {
      var Ctor = createCtorWrapper(func);

      function wrapper() {
        var length = arguments.length,
            args = Array(length),
            index = length,
            placeholder = getHolder(wrapper);

        while (index--) {
          args[index] = arguments[index];
        }
        var holders = (length < 3 && args[0] !== placeholder && args[length - 1] !== placeholder)
          ? []
          : replaceHolders(args, placeholder);

        length -= holders.length;
        if (length < arity) {
          return createRecurryWrapper(
            func, bitmask, createHybridWrapper, wrapper.placeholder, undefined,
            args, holders, undefined, undefined, arity - length);
        }
        var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
        return apply(fn, this, args);
      }
      return wrapper;
    }

    /**
     * Creates a `_.flow` or `_.flowRight` function.
     *
     * @private
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new flow function.
     */
    function createFlow(fromRight) {
      return rest(function(funcs) {
        funcs = baseFlatten(funcs, 1);

        var length = funcs.length,
            index = length,
            prereq = LodashWrapper.prototype.thru;

        if (fromRight) {
          funcs.reverse();
        }
        while (index--) {
          var func = funcs[index];
          if (typeof func != 'function') {
            throw new TypeError(FUNC_ERROR_TEXT);
          }
          if (prereq && !wrapper && getFuncName(func) == 'wrapper') {
            var wrapper = new LodashWrapper([], true);
          }
        }
        index = wrapper ? index : length;
        while (++index < length) {
          func = funcs[index];

          var funcName = getFuncName(func),
              data = funcName == 'wrapper' ? getData(func) : undefined;

          if (data && isLaziable(data[0]) &&
                data[1] == (ARY_FLAG | CURRY_FLAG | PARTIAL_FLAG | REARG_FLAG) &&
                !data[4].length && data[9] == 1
              ) {
            wrapper = wrapper[getFuncName(data[0])].apply(wrapper, data[3]);
          } else {
            wrapper = (func.length == 1 && isLaziable(func))
              ? wrapper[funcName]()
              : wrapper.thru(func);
          }
        }
        return function() {
          var args = arguments,
              value = args[0];

          if (wrapper && args.length == 1 &&
              isArray(value) && value.length >= LARGE_ARRAY_SIZE) {
            return wrapper.plant(value).value();
          }
          var index = 0,
              result = length ? funcs[index].apply(this, args) : value;

          while (++index < length) {
            result = funcs[index].call(this, result);
          }
          return result;
        };
      });
    }

    /**
     * Creates a function that wraps `func` to invoke it with optional `this`
     * binding of `thisArg`, partial application, and currying.
     *
     * @private
     * @param {Function|string} func The function or method name to wrap.
     * @param {number} bitmask The bitmask of wrapper flags. See `createWrapper`
     *  for more details.
     * @param {*} [thisArg] The `this` binding of `func`.
     * @param {Array} [partials] The arguments to prepend to those provided to
     *  the new function.
     * @param {Array} [holders] The `partials` placeholder indexes.
     * @param {Array} [partialsRight] The arguments to append to those provided
     *  to the new function.
     * @param {Array} [holdersRight] The `partialsRight` placeholder indexes.
     * @param {Array} [argPos] The argument positions of the new function.
     * @param {number} [ary] The arity cap of `func`.
     * @param {number} [arity] The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createHybridWrapper(func, bitmask, thisArg, partials, holders, partialsRight, holdersRight, argPos, ary, arity) {
      var isAry = bitmask & ARY_FLAG,
          isBind = bitmask & BIND_FLAG,
          isBindKey = bitmask & BIND_KEY_FLAG,
          isCurried = bitmask & (CURRY_FLAG | CURRY_RIGHT_FLAG),
          isFlip = bitmask & FLIP_FLAG,
          Ctor = isBindKey ? undefined : createCtorWrapper(func);

      function wrapper() {
        var length = arguments.length,
            args = Array(length),
            index = length;

        while (index--) {
          args[index] = arguments[index];
        }
        if (isCurried) {
          var placeholder = getHolder(wrapper),
              holdersCount = countHolders(args, placeholder);
        }
        if (partials) {
          args = composeArgs(args, partials, holders, isCurried);
        }
        if (partialsRight) {
          args = composeArgsRight(args, partialsRight, holdersRight, isCurried);
        }
        length -= holdersCount;
        if (isCurried && length < arity) {
          var newHolders = replaceHolders(args, placeholder);
          return createRecurryWrapper(
            func, bitmask, createHybridWrapper, wrapper.placeholder, thisArg,
            args, newHolders, argPos, ary, arity - length
          );
        }
        var thisBinding = isBind ? thisArg : this,
            fn = isBindKey ? thisBinding[func] : func;

        length = args.length;
        if (argPos) {
          args = reorder(args, argPos);
        } else if (isFlip && length > 1) {
          args.reverse();
        }
        if (isAry && ary < length) {
          args.length = ary;
        }
        if (this && this !== root && this instanceof wrapper) {
          fn = Ctor || createCtorWrapper(fn);
        }
        return fn.apply(thisBinding, args);
      }
      return wrapper;
    }

    /**
     * Creates a function like `_.invertBy`.
     *
     * @private
     * @param {Function} setter The function to set accumulator values.
     * @param {Function} toIteratee The function to resolve iteratees.
     * @returns {Function} Returns the new inverter function.
     */
    function createInverter(setter, toIteratee) {
      return function(object, iteratee) {
        return baseInverter(object, setter, toIteratee(iteratee), {});
      };
    }

    /**
     * Creates a function that performs a mathematical operation on two values.
     *
     * @private
     * @param {Function} operator The function to perform the operation.
     * @returns {Function} Returns the new mathematical operation function.
     */
    function createMathOperation(operator) {
      return function(value, other) {
        var result;
        if (value === undefined && other === undefined) {
          return 0;
        }
        if (value !== undefined) {
          result = value;
        }
        if (other !== undefined) {
          if (result === undefined) {
            return other;
          }
          if (typeof value == 'string' || typeof other == 'string') {
            value = baseToString(value);
            other = baseToString(other);
          } else {
            value = baseToNumber(value);
            other = baseToNumber(other);
          }
          result = operator(value, other);
        }
        return result;
      };
    }

    /**
     * Creates a function like `_.over`.
     *
     * @private
     * @param {Function} arrayFunc The function to iterate over iteratees.
     * @returns {Function} Returns the new over function.
     */
    function createOver(arrayFunc) {
      return rest(function(iteratees) {
        iteratees = (iteratees.length == 1 && isArray(iteratees[0]))
          ? arrayMap(iteratees[0], baseUnary(getIteratee()))
          : arrayMap(baseFlatten(iteratees, 1, isFlattenableIteratee), baseUnary(getIteratee()));

        return rest(function(args) {
          var thisArg = this;
          return arrayFunc(iteratees, function(iteratee) {
            return apply(iteratee, thisArg, args);
          });
        });
      });
    }

    /**
     * Creates the padding for `string` based on `length`. The `chars` string
     * is truncated if the number of characters exceeds `length`.
     *
     * @private
     * @param {number} length The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padding for `string`.
     */
    function createPadding(length, chars) {
      chars = chars === undefined ? ' ' : baseToString(chars);

      var charsLength = chars.length;
      if (charsLength < 2) {
        return charsLength ? baseRepeat(chars, length) : chars;
      }
      var result = baseRepeat(chars, nativeCeil(length / stringSize(chars)));
      return reHasComplexSymbol.test(chars)
        ? castSlice(stringToArray(result), 0, length).join('')
        : result.slice(0, length);
    }

    /**
     * Creates a function that wraps `func` to invoke it with the `this` binding
     * of `thisArg` and `partials` prepended to the arguments it receives.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask of wrapper flags. See `createWrapper`
     *  for more details.
     * @param {*} thisArg The `this` binding of `func`.
     * @param {Array} partials The arguments to prepend to those provided to
     *  the new function.
     * @returns {Function} Returns the new wrapped function.
     */
    function createPartialWrapper(func, bitmask, thisArg, partials) {
      var isBind = bitmask & BIND_FLAG,
          Ctor = createCtorWrapper(func);

      function wrapper() {
        var argsIndex = -1,
            argsLength = arguments.length,
            leftIndex = -1,
            leftLength = partials.length,
            args = Array(leftLength + argsLength),
            fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;

        while (++leftIndex < leftLength) {
          args[leftIndex] = partials[leftIndex];
        }
        while (argsLength--) {
          args[leftIndex++] = arguments[++argsIndex];
        }
        return apply(fn, isBind ? thisArg : this, args);
      }
      return wrapper;
    }

    /**
     * Creates a `_.range` or `_.rangeRight` function.
     *
     * @private
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new range function.
     */
    function createRange(fromRight) {
      return function(start, end, step) {
        if (step && typeof step != 'number' && isIterateeCall(start, end, step)) {
          end = step = undefined;
        }
        // Ensure the sign of `-0` is preserved.
        start = toNumber(start);
        start = start === start ? start : 0;
        if (end === undefined) {
          end = start;
          start = 0;
        } else {
          end = toNumber(end) || 0;
        }
        step = step === undefined ? (start < end ? 1 : -1) : (toNumber(step) || 0);
        return baseRange(start, end, step, fromRight);
      };
    }

    /**
     * Creates a function that performs a relational operation on two values.
     *
     * @private
     * @param {Function} operator The function to perform the operation.
     * @returns {Function} Returns the new relational operation function.
     */
    function createRelationalOperation(operator) {
      return function(value, other) {
        if (!(typeof value == 'string' && typeof other == 'string')) {
          value = toNumber(value);
          other = toNumber(other);
        }
        return operator(value, other);
      };
    }

    /**
     * Creates a function that wraps `func` to continue currying.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask of wrapper flags. See `createWrapper`
     *  for more details.
     * @param {Function} wrapFunc The function to create the `func` wrapper.
     * @param {*} placeholder The placeholder value.
     * @param {*} [thisArg] The `this` binding of `func`.
     * @param {Array} [partials] The arguments to prepend to those provided to
     *  the new function.
     * @param {Array} [holders] The `partials` placeholder indexes.
     * @param {Array} [argPos] The argument positions of the new function.
     * @param {number} [ary] The arity cap of `func`.
     * @param {number} [arity] The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createRecurryWrapper(func, bitmask, wrapFunc, placeholder, thisArg, partials, holders, argPos, ary, arity) {
      var isCurry = bitmask & CURRY_FLAG,
          newHolders = isCurry ? holders : undefined,
          newHoldersRight = isCurry ? undefined : holders,
          newPartials = isCurry ? partials : undefined,
          newPartialsRight = isCurry ? undefined : partials;

      bitmask |= (isCurry ? PARTIAL_FLAG : PARTIAL_RIGHT_FLAG);
      bitmask &= ~(isCurry ? PARTIAL_RIGHT_FLAG : PARTIAL_FLAG);

      if (!(bitmask & CURRY_BOUND_FLAG)) {
        bitmask &= ~(BIND_FLAG | BIND_KEY_FLAG);
      }
      var newData = [
        func, bitmask, thisArg, newPartials, newHolders, newPartialsRight,
        newHoldersRight, argPos, ary, arity
      ];

      var result = wrapFunc.apply(undefined, newData);
      if (isLaziable(func)) {
        setData(result, newData);
      }
      result.placeholder = placeholder;
      return result;
    }

    /**
     * Creates a function like `_.round`.
     *
     * @private
     * @param {string} methodName The name of the `Math` method to use when rounding.
     * @returns {Function} Returns the new round function.
     */
    function createRound(methodName) {
      var func = Math[methodName];
      return function(number, precision) {
        number = toNumber(number);
        precision = toInteger(precision);
        if (precision) {
          // Shift with exponential notation to avoid floating-point issues.
          // See [MDN](https://mdn.io/round#Examples) for more details.
          var pair = (toString(number) + 'e').split('e'),
              value = func(pair[0] + 'e' + (+pair[1] + precision));

          pair = (toString(value) + 'e').split('e');
          return +(pair[0] + 'e' + (+pair[1] - precision));
        }
        return func(number);
      };
    }

    /**
     * Creates a set of `values`.
     *
     * @private
     * @param {Array} values The values to add to the set.
     * @returns {Object} Returns the new set.
     */
    var createSet = !(Set && (1 / setToArray(new Set([,-0]))[1]) == INFINITY) ? noop : function(values) {
      return new Set(values);
    };

    /**
     * Creates a `_.toPairs` or `_.toPairsIn` function.
     *
     * @private
     * @param {Function} keysFunc The function to get the keys of a given object.
     * @returns {Function} Returns the new pairs function.
     */
    function createToPairs(keysFunc) {
      return function(object) {
        var tag = getTag(object);
        if (tag == mapTag) {
          return mapToArray(object);
        }
        if (tag == setTag) {
          return setToPairs(object);
        }
        return baseToPairs(object, keysFunc(object));
      };
    }

    /**
     * Creates a function that either curries or invokes `func` with optional
     * `this` binding and partially applied arguments.
     *
     * @private
     * @param {Function|string} func The function or method name to wrap.
     * @param {number} bitmask The bitmask of wrapper flags.
     *  The bitmask may be composed of the following flags:
     *     1 - `_.bind`
     *     2 - `_.bindKey`
     *     4 - `_.curry` or `_.curryRight` of a bound function
     *     8 - `_.curry`
     *    16 - `_.curryRight`
     *    32 - `_.partial`
     *    64 - `_.partialRight`
     *   128 - `_.rearg`
     *   256 - `_.ary`
     *   512 - `_.flip`
     * @param {*} [thisArg] The `this` binding of `func`.
     * @param {Array} [partials] The arguments to be partially applied.
     * @param {Array} [holders] The `partials` placeholder indexes.
     * @param {Array} [argPos] The argument positions of the new function.
     * @param {number} [ary] The arity cap of `func`.
     * @param {number} [arity] The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createWrapper(func, bitmask, thisArg, partials, holders, argPos, ary, arity) {
      var isBindKey = bitmask & BIND_KEY_FLAG;
      if (!isBindKey && typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      var length = partials ? partials.length : 0;
      if (!length) {
        bitmask &= ~(PARTIAL_FLAG | PARTIAL_RIGHT_FLAG);
        partials = holders = undefined;
      }
      ary = ary === undefined ? ary : nativeMax(toInteger(ary), 0);
      arity = arity === undefined ? arity : toInteger(arity);
      length -= holders ? holders.length : 0;

      if (bitmask & PARTIAL_RIGHT_FLAG) {
        var partialsRight = partials,
            holdersRight = holders;

        partials = holders = undefined;
      }
      var data = isBindKey ? undefined : getData(func);

      var newData = [
        func, bitmask, thisArg, partials, holders, partialsRight, holdersRight,
        argPos, ary, arity
      ];

      if (data) {
        mergeData(newData, data);
      }
      func = newData[0];
      bitmask = newData[1];
      thisArg = newData[2];
      partials = newData[3];
      holders = newData[4];
      arity = newData[9] = newData[9] == null
        ? (isBindKey ? 0 : func.length)
        : nativeMax(newData[9] - length, 0);

      if (!arity && bitmask & (CURRY_FLAG | CURRY_RIGHT_FLAG)) {
        bitmask &= ~(CURRY_FLAG | CURRY_RIGHT_FLAG);
      }
      if (!bitmask || bitmask == BIND_FLAG) {
        var result = createBaseWrapper(func, bitmask, thisArg);
      } else if (bitmask == CURRY_FLAG || bitmask == CURRY_RIGHT_FLAG) {
        result = createCurryWrapper(func, bitmask, arity);
      } else if ((bitmask == PARTIAL_FLAG || bitmask == (BIND_FLAG | PARTIAL_FLAG)) && !holders.length) {
        result = createPartialWrapper(func, bitmask, thisArg, partials);
      } else {
        result = createHybridWrapper.apply(undefined, newData);
      }
      var setter = data ? baseSetData : setData;
      return setter(result, newData);
    }

    /**
     * A specialized version of `baseIsEqualDeep` for arrays with support for
     * partial deep comparisons.
     *
     * @private
     * @param {Array} array The array to compare.
     * @param {Array} other The other array to compare.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Function} customizer The function to customize comparisons.
     * @param {number} bitmask The bitmask of comparison flags. See `baseIsEqual`
     *  for more details.
     * @param {Object} stack Tracks traversed `array` and `other` objects.
     * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
     */
    function equalArrays(array, other, equalFunc, customizer, bitmask, stack) {
      var isPartial = bitmask & PARTIAL_COMPARE_FLAG,
          arrLength = array.length,
          othLength = other.length;

      if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(array);
      if (stacked) {
        return stacked == other;
      }
      var index = -1,
          result = true,
          seen = (bitmask & UNORDERED_COMPARE_FLAG) ? new SetCache : undefined;

      stack.set(array, other);

      // Ignore non-index properties.
      while (++index < arrLength) {
        var arrValue = array[index],
            othValue = other[index];

        if (customizer) {
          var compared = isPartial
            ? customizer(othValue, arrValue, index, other, array, stack)
            : customizer(arrValue, othValue, index, array, other, stack);
        }
        if (compared !== undefined) {
          if (compared) {
            continue;
          }
          result = false;
          break;
        }
        // Recursively compare arrays (susceptible to call stack limits).
        if (seen) {
          if (!arraySome(other, function(othValue, othIndex) {
                if (!seen.has(othIndex) &&
                    (arrValue === othValue || equalFunc(arrValue, othValue, customizer, bitmask, stack))) {
                  return seen.add(othIndex);
                }
              })) {
            result = false;
            break;
          }
        } else if (!(
              arrValue === othValue ||
                equalFunc(arrValue, othValue, customizer, bitmask, stack)
            )) {
          result = false;
          break;
        }
      }
      stack['delete'](array);
      return result;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for comparing objects of
     * the same `toStringTag`.
     *
     * **Note:** This function only supports comparing values with tags of
     * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {string} tag The `toStringTag` of the objects to compare.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Function} customizer The function to customize comparisons.
     * @param {number} bitmask The bitmask of comparison flags. See `baseIsEqual`
     *  for more details.
     * @param {Object} stack Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function equalByTag(object, other, tag, equalFunc, customizer, bitmask, stack) {
      switch (tag) {
        case dataViewTag:
          if ((object.byteLength != other.byteLength) ||
              (object.byteOffset != other.byteOffset)) {
            return false;
          }
          object = object.buffer;
          other = other.buffer;

        case arrayBufferTag:
          if ((object.byteLength != other.byteLength) ||
              !equalFunc(new Uint8Array(object), new Uint8Array(other))) {
            return false;
          }
          return true;

        case boolTag:
        case dateTag:
          // Coerce dates and booleans to numbers, dates to milliseconds and
          // booleans to `1` or `0` treating invalid dates coerced to `NaN` as
          // not equal.
          return +object == +other;

        case errorTag:
          return object.name == other.name && object.message == other.message;

        case numberTag:
          // Treat `NaN` vs. `NaN` as equal.
          return (object != +object) ? other != +other : object == +other;

        case regexpTag:
        case stringTag:
          // Coerce regexes to strings and treat strings, primitives and objects,
          // as equal. See http://www.ecma-international.org/ecma-262/6.0/#sec-regexp.prototype.tostring
          // for more details.
          return object == (other + '');

        case mapTag:
          var convert = mapToArray;

        case setTag:
          var isPartial = bitmask & PARTIAL_COMPARE_FLAG;
          convert || (convert = setToArray);

          if (object.size != other.size && !isPartial) {
            return false;
          }
          // Assume cyclic values are equal.
          var stacked = stack.get(object);
          if (stacked) {
            return stacked == other;
          }
          bitmask |= UNORDERED_COMPARE_FLAG;
          stack.set(object, other);

          // Recursively compare objects (susceptible to call stack limits).
          return equalArrays(convert(object), convert(other), equalFunc, customizer, bitmask, stack);

        case symbolTag:
          if (symbolValueOf) {
            return symbolValueOf.call(object) == symbolValueOf.call(other);
          }
      }
      return false;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for objects with support for
     * partial deep comparisons.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Function} customizer The function to customize comparisons.
     * @param {number} bitmask The bitmask of comparison flags. See `baseIsEqual`
     *  for more details.
     * @param {Object} stack Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function equalObjects(object, other, equalFunc, customizer, bitmask, stack) {
      var isPartial = bitmask & PARTIAL_COMPARE_FLAG,
          objProps = keys(object),
          objLength = objProps.length,
          othProps = keys(other),
          othLength = othProps.length;

      if (objLength != othLength && !isPartial) {
        return false;
      }
      var index = objLength;
      while (index--) {
        var key = objProps[index];
        if (!(isPartial ? key in other : baseHas(other, key))) {
          return false;
        }
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked) {
        return stacked == other;
      }
      var result = true;
      stack.set(object, other);

      var skipCtor = isPartial;
      while (++index < objLength) {
        key = objProps[index];
        var objValue = object[key],
            othValue = other[key];

        if (customizer) {
          var compared = isPartial
            ? customizer(othValue, objValue, key, other, object, stack)
            : customizer(objValue, othValue, key, object, other, stack);
        }
        // Recursively compare objects (susceptible to call stack limits).
        if (!(compared === undefined
              ? (objValue === othValue || equalFunc(objValue, othValue, customizer, bitmask, stack))
              : compared
            )) {
          result = false;
          break;
        }
        skipCtor || (skipCtor = key == 'constructor');
      }
      if (result && !skipCtor) {
        var objCtor = object.constructor,
            othCtor = other.constructor;

        // Non `Object` object instances with different constructors are not equal.
        if (objCtor != othCtor &&
            ('constructor' in object && 'constructor' in other) &&
            !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
              typeof othCtor == 'function' && othCtor instanceof othCtor)) {
          result = false;
        }
      }
      stack['delete'](object);
      return result;
    }

    /**
     * Creates an array of own enumerable property names and symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function getAllKeys(object) {
      return baseGetAllKeys(object, keys, getSymbols);
    }

    /**
     * Creates an array of own and inherited enumerable property names and
     * symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function getAllKeysIn(object) {
      return baseGetAllKeys(object, keysIn, getSymbolsIn);
    }

    /**
     * Gets metadata for `func`.
     *
     * @private
     * @param {Function} func The function to query.
     * @returns {*} Returns the metadata for `func`.
     */
    var getData = !metaMap ? noop : function(func) {
      return metaMap.get(func);
    };

    /**
     * Gets the name of `func`.
     *
     * @private
     * @param {Function} func The function to query.
     * @returns {string} Returns the function name.
     */
    function getFuncName(func) {
      var result = (func.name + ''),
          array = realNames[result],
          length = hasOwnProperty.call(realNames, result) ? array.length : 0;

      while (length--) {
        var data = array[length],
            otherFunc = data.func;
        if (otherFunc == null || otherFunc == func) {
          return data.name;
        }
      }
      return result;
    }

    /**
     * Gets the argument placeholder value for `func`.
     *
     * @private
     * @param {Function} func The function to inspect.
     * @returns {*} Returns the placeholder value.
     */
    function getHolder(func) {
      var object = hasOwnProperty.call(lodash, 'placeholder') ? lodash : func;
      return object.placeholder;
    }

    /**
     * Gets the appropriate "iteratee" function. If `_.iteratee` is customized,
     * this function returns the custom method, otherwise it returns `baseIteratee`.
     * If arguments are provided, the chosen function is invoked with them and
     * its result is returned.
     *
     * @private
     * @param {*} [value] The value to convert to an iteratee.
     * @param {number} [arity] The arity of the created iteratee.
     * @returns {Function} Returns the chosen function or its result.
     */
    function getIteratee() {
      var result = lodash.iteratee || iteratee;
      result = result === iteratee ? baseIteratee : result;
      return arguments.length ? result(arguments[0], arguments[1]) : result;
    }

    /**
     * Gets the "length" property value of `object`.
     *
     * **Note:** This function is used to avoid a
     * [JIT bug](https://bugs.webkit.org/show_bug.cgi?id=142792) that affects
     * Safari on at least iOS 8.1-8.3 ARM64.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {*} Returns the "length" value.
     */
    var getLength = baseProperty('length');

    /**
     * Gets the data for `map`.
     *
     * @private
     * @param {Object} map The map to query.
     * @param {string} key The reference key.
     * @returns {*} Returns the map data.
     */
    function getMapData(map, key) {
      var data = map.__data__;
      return isKeyable(key)
        ? data[typeof key == 'string' ? 'string' : 'hash']
        : data.map;
    }

    /**
     * Gets the property names, values, and compare flags of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the match data of `object`.
     */
    function getMatchData(object) {
      var result = toPairs(object),
          length = result.length;

      while (length--) {
        result[length][2] = isStrictComparable(result[length][1]);
      }
      return result;
    }

    /**
     * Gets the native function at `key` of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {string} key The key of the method to get.
     * @returns {*} Returns the function if it's native, else `undefined`.
     */
    function getNative(object, key) {
      var value = object[key];
      return isNative(value) ? value : undefined;
    }

    /**
     * Gets the `[[Prototype]]` of `value`.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {null|Object} Returns the `[[Prototype]]`.
     */
    function getPrototype(value) {
      return nativeGetPrototype(Object(value));
    }

    /**
     * Creates an array of the own enumerable symbol properties of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of symbols.
     */
    function getSymbols(object) {
      // Coerce `object` to an object to avoid non-object errors in V8.
      // See https://bugs.chromium.org/p/v8/issues/detail?id=3443 for more details.
      return getOwnPropertySymbols(Object(object));
    }

    // Fallback for IE < 11.
    if (!getOwnPropertySymbols) {
      getSymbols = function() {
        return [];
      };
    }

    /**
     * Creates an array of the own and inherited enumerable symbol properties
     * of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of symbols.
     */
    var getSymbolsIn = !getOwnPropertySymbols ? getSymbols : function(object) {
      var result = [];
      while (object) {
        arrayPush(result, getSymbols(object));
        object = getPrototype(object);
      }
      return result;
    };

    /**
     * Gets the `toStringTag` of `value`.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {string} Returns the `toStringTag`.
     */
    function getTag(value) {
      return objectToString.call(value);
    }

    // Fallback for data views, maps, sets, and weak maps in IE 11,
    // for data views in Edge, and promises in Node.js.
    if ((DataView && getTag(new DataView(new ArrayBuffer(1))) != dataViewTag) ||
        (Map && getTag(new Map) != mapTag) ||
        (Promise && getTag(Promise.resolve()) != promiseTag) ||
        (Set && getTag(new Set) != setTag) ||
        (WeakMap && getTag(new WeakMap) != weakMapTag)) {
      getTag = function(value) {
        var result = objectToString.call(value),
            Ctor = result == objectTag ? value.constructor : undefined,
            ctorString = Ctor ? toSource(Ctor) : undefined;

        if (ctorString) {
          switch (ctorString) {
            case dataViewCtorString: return dataViewTag;
            case mapCtorString: return mapTag;
            case promiseCtorString: return promiseTag;
            case setCtorString: return setTag;
            case weakMapCtorString: return weakMapTag;
          }
        }
        return result;
      };
    }

    /**
     * Gets the view, applying any `transforms` to the `start` and `end` positions.
     *
     * @private
     * @param {number} start The start of the view.
     * @param {number} end The end of the view.
     * @param {Array} transforms The transformations to apply to the view.
     * @returns {Object} Returns an object containing the `start` and `end`
     *  positions of the view.
     */
    function getView(start, end, transforms) {
      var index = -1,
          length = transforms.length;

      while (++index < length) {
        var data = transforms[index],
            size = data.size;

        switch (data.type) {
          case 'drop':      start += size; break;
          case 'dropRight': end -= size; break;
          case 'take':      end = nativeMin(end, start + size); break;
          case 'takeRight': start = nativeMax(start, end - size); break;
        }
      }
      return { 'start': start, 'end': end };
    }

    /**
     * Checks if `path` exists on `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path to check.
     * @param {Function} hasFunc The function to check properties.
     * @returns {boolean} Returns `true` if `path` exists, else `false`.
     */
    function hasPath(object, path, hasFunc) {
      path = isKey(path, object) ? [path] : castPath(path);

      var result,
          index = -1,
          length = path.length;

      while (++index < length) {
        var key = toKey(path[index]);
        if (!(result = object != null && hasFunc(object, key))) {
          break;
        }
        object = object[key];
      }
      if (result) {
        return result;
      }
      var length = object ? object.length : 0;
      return !!length && isLength(length) && isIndex(key, length) &&
        (isArray(object) || isString(object) || isArguments(object));
    }

    /**
     * Initializes an array clone.
     *
     * @private
     * @param {Array} array The array to clone.
     * @returns {Array} Returns the initialized clone.
     */
    function initCloneArray(array) {
      var length = array.length,
          result = array.constructor(length);

      // Add properties assigned by `RegExp#exec`.
      if (length && typeof array[0] == 'string' && hasOwnProperty.call(array, 'index')) {
        result.index = array.index;
        result.input = array.input;
      }
      return result;
    }

    /**
     * Initializes an object clone.
     *
     * @private
     * @param {Object} object The object to clone.
     * @returns {Object} Returns the initialized clone.
     */
    function initCloneObject(object) {
      return (typeof object.constructor == 'function' && !isPrototype(object))
        ? baseCreate(getPrototype(object))
        : {};
    }

    /**
     * Initializes an object clone based on its `toStringTag`.
     *
     * **Note:** This function only supports cloning values with tags of
     * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
     *
     * @private
     * @param {Object} object The object to clone.
     * @param {string} tag The `toStringTag` of the object to clone.
     * @param {Function} cloneFunc The function to clone values.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the initialized clone.
     */
    function initCloneByTag(object, tag, cloneFunc, isDeep) {
      var Ctor = object.constructor;
      switch (tag) {
        case arrayBufferTag:
          return cloneArrayBuffer(object);

        case boolTag:
        case dateTag:
          return new Ctor(+object);

        case dataViewTag:
          return cloneDataView(object, isDeep);

        case float32Tag: case float64Tag:
        case int8Tag: case int16Tag: case int32Tag:
        case uint8Tag: case uint8ClampedTag: case uint16Tag: case uint32Tag:
          return cloneTypedArray(object, isDeep);

        case mapTag:
          return cloneMap(object, isDeep, cloneFunc);

        case numberTag:
        case stringTag:
          return new Ctor(object);

        case regexpTag:
          return cloneRegExp(object);

        case setTag:
          return cloneSet(object, isDeep, cloneFunc);

        case symbolTag:
          return cloneSymbol(object);
      }
    }

    /**
     * Creates an array of index keys for `object` values of arrays,
     * `arguments` objects, and strings, otherwise `null` is returned.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array|null} Returns index keys, else `null`.
     */
    function indexKeys(object) {
      var length = object ? object.length : undefined;
      if (isLength(length) &&
          (isArray(object) || isString(object) || isArguments(object))) {
        return baseTimes(length, String);
      }
      return null;
    }

    /**
     * Checks if `value` is a flattenable `arguments` object or array.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
     */
    function isFlattenable(value) {
      return isArray(value) || isArguments(value);
    }

    /**
     * Checks if `value` is a flattenable array and not a `_.matchesProperty`
     * iteratee shorthand.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
     */
    function isFlattenableIteratee(value) {
      return isArray(value) && !(value.length == 2 && !isFunction(value[0]));
    }

    /**
     * Checks if `value` is a valid array-like index.
     *
     * @private
     * @param {*} value The value to check.
     * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
     * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
     */
    function isIndex(value, length) {
      length = length == null ? MAX_SAFE_INTEGER : length;
      return !!length &&
        (typeof value == 'number' || reIsUint.test(value)) &&
        (value > -1 && value % 1 == 0 && value < length);
    }

    /**
     * Checks if the given arguments are from an iteratee call.
     *
     * @private
     * @param {*} value The potential iteratee value argument.
     * @param {*} index The potential iteratee index or key argument.
     * @param {*} object The potential iteratee object argument.
     * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
     *  else `false`.
     */
    function isIterateeCall(value, index, object) {
      if (!isObject(object)) {
        return false;
      }
      var type = typeof index;
      if (type == 'number'
            ? (isArrayLike(object) && isIndex(index, object.length))
            : (type == 'string' && index in object)
          ) {
        return eq(object[index], value);
      }
      return false;
    }

    /**
     * Checks if `value` is a property name and not a property path.
     *
     * @private
     * @param {*} value The value to check.
     * @param {Object} [object] The object to query keys on.
     * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
     */
    function isKey(value, object) {
      if (isArray(value)) {
        return false;
      }
      var type = typeof value;
      if (type == 'number' || type == 'symbol' || type == 'boolean' ||
          value == null || isSymbol(value)) {
        return true;
      }
      return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
        (object != null && value in Object(object));
    }

    /**
     * Checks if `value` is suitable for use as unique object key.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
     */
    function isKeyable(value) {
      var type = typeof value;
      return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
        ? (value !== '__proto__')
        : (value === null);
    }

    /**
     * Checks if `func` has a lazy counterpart.
     *
     * @private
     * @param {Function} func The function to check.
     * @returns {boolean} Returns `true` if `func` has a lazy counterpart,
     *  else `false`.
     */
    function isLaziable(func) {
      var funcName = getFuncName(func),
          other = lodash[funcName];

      if (typeof other != 'function' || !(funcName in LazyWrapper.prototype)) {
        return false;
      }
      if (func === other) {
        return true;
      }
      var data = getData(other);
      return !!data && func === data[0];
    }

    /**
     * Checks if `value` is likely a prototype object.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
     */
    function isPrototype(value) {
      var Ctor = value && value.constructor,
          proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto;

      return value === proto;
    }

    /**
     * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` if suitable for strict
     *  equality comparisons, else `false`.
     */
    function isStrictComparable(value) {
      return value === value && !isObject(value);
    }

    /**
     * A specialized version of `matchesProperty` for source values suitable
     * for strict equality comparisons, i.e. `===`.
     *
     * @private
     * @param {string} key The key of the property to get.
     * @param {*} srcValue The value to match.
     * @returns {Function} Returns the new spec function.
     */
    function matchesStrictComparable(key, srcValue) {
      return function(object) {
        if (object == null) {
          return false;
        }
        return object[key] === srcValue &&
          (srcValue !== undefined || (key in Object(object)));
      };
    }

    /**
     * Merges the function metadata of `source` into `data`.
     *
     * Merging metadata reduces the number of wrappers used to invoke a function.
     * This is possible because methods like `_.bind`, `_.curry`, and `_.partial`
     * may be applied regardless of execution order. Methods like `_.ary` and
     * `_.rearg` modify function arguments, making the order in which they are
     * executed important, preventing the merging of metadata. However, we make
     * an exception for a safe combined case where curried functions have `_.ary`
     * and or `_.rearg` applied.
     *
     * @private
     * @param {Array} data The destination metadata.
     * @param {Array} source The source metadata.
     * @returns {Array} Returns `data`.
     */
    function mergeData(data, source) {
      var bitmask = data[1],
          srcBitmask = source[1],
          newBitmask = bitmask | srcBitmask,
          isCommon = newBitmask < (BIND_FLAG | BIND_KEY_FLAG | ARY_FLAG);

      var isCombo =
        ((srcBitmask == ARY_FLAG) && (bitmask == CURRY_FLAG)) ||
        ((srcBitmask == ARY_FLAG) && (bitmask == REARG_FLAG) && (data[7].length <= source[8])) ||
        ((srcBitmask == (ARY_FLAG | REARG_FLAG)) && (source[7].length <= source[8]) && (bitmask == CURRY_FLAG));

      // Exit early if metadata can't be merged.
      if (!(isCommon || isCombo)) {
        return data;
      }
      // Use source `thisArg` if available.
      if (srcBitmask & BIND_FLAG) {
        data[2] = source[2];
        // Set when currying a bound function.
        newBitmask |= bitmask & BIND_FLAG ? 0 : CURRY_BOUND_FLAG;
      }
      // Compose partial arguments.
      var value = source[3];
      if (value) {
        var partials = data[3];
        data[3] = partials ? composeArgs(partials, value, source[4]) : value;
        data[4] = partials ? replaceHolders(data[3], PLACEHOLDER) : source[4];
      }
      // Compose partial right arguments.
      value = source[5];
      if (value) {
        partials = data[5];
        data[5] = partials ? composeArgsRight(partials, value, source[6]) : value;
        data[6] = partials ? replaceHolders(data[5], PLACEHOLDER) : source[6];
      }
      // Use source `argPos` if available.
      value = source[7];
      if (value) {
        data[7] = value;
      }
      // Use source `ary` if it's smaller.
      if (srcBitmask & ARY_FLAG) {
        data[8] = data[8] == null ? source[8] : nativeMin(data[8], source[8]);
      }
      // Use source `arity` if one is not provided.
      if (data[9] == null) {
        data[9] = source[9];
      }
      // Use source `func` and merge bitmasks.
      data[0] = source[0];
      data[1] = newBitmask;

      return data;
    }

    /**
     * Used by `_.defaultsDeep` to customize its `_.merge` use.
     *
     * @private
     * @param {*} objValue The destination value.
     * @param {*} srcValue The source value.
     * @param {string} key The key of the property to merge.
     * @param {Object} object The parent object of `objValue`.
     * @param {Object} source The parent object of `srcValue`.
     * @param {Object} [stack] Tracks traversed source values and their merged
     *  counterparts.
     * @returns {*} Returns the value to assign.
     */
    function mergeDefaults(objValue, srcValue, key, object, source, stack) {
      if (isObject(objValue) && isObject(srcValue)) {
        baseMerge(objValue, srcValue, undefined, mergeDefaults, stack.set(srcValue, objValue));
      }
      return objValue;
    }

    /**
     * Gets the parent value at `path` of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array} path The path to get the parent value of.
     * @returns {*} Returns the parent value.
     */
    function parent(object, path) {
      return path.length == 1 ? object : baseGet(object, baseSlice(path, 0, -1));
    }

    /**
     * Reorder `array` according to the specified indexes where the element at
     * the first index is assigned as the first element, the element at
     * the second index is assigned as the second element, and so on.
     *
     * @private
     * @param {Array} array The array to reorder.
     * @param {Array} indexes The arranged array indexes.
     * @returns {Array} Returns `array`.
     */
    function reorder(array, indexes) {
      var arrLength = array.length,
          length = nativeMin(indexes.length, arrLength),
          oldArray = copyArray(array);

      while (length--) {
        var index = indexes[length];
        array[length] = isIndex(index, arrLength) ? oldArray[index] : undefined;
      }
      return array;
    }

    /**
     * Sets metadata for `func`.
     *
     * **Note:** If this function becomes hot, i.e. is invoked a lot in a short
     * period of time, it will trip its breaker and transition to an identity
     * function to avoid garbage collection pauses in V8. See
     * [V8 issue 2070](https://bugs.chromium.org/p/v8/issues/detail?id=2070)
     * for more details.
     *
     * @private
     * @param {Function} func The function to associate metadata with.
     * @param {*} data The metadata.
     * @returns {Function} Returns `func`.
     */
    var setData = (function() {
      var count = 0,
          lastCalled = 0;

      return function(key, value) {
        var stamp = now(),
            remaining = HOT_SPAN - (stamp - lastCalled);

        lastCalled = stamp;
        if (remaining > 0) {
          if (++count >= HOT_COUNT) {
            return key;
          }
        } else {
          count = 0;
        }
        return baseSetData(key, value);
      };
    }());

    /**
     * Converts `string` to a property path array.
     *
     * @private
     * @param {string} string The string to convert.
     * @returns {Array} Returns the property path array.
     */
    var stringToPath = memoize(function(string) {
      var result = [];
      toString(string).replace(rePropName, function(match, number, quote, string) {
        result.push(quote ? string.replace(reEscapeChar, '$1') : (number || match));
      });
      return result;
    });

    /**
     * Converts `value` to a string key if it's not a string or symbol.
     *
     * @private
     * @param {*} value The value to inspect.
     * @returns {string|symbol} Returns the key.
     */
    function toKey(value) {
      if (typeof value == 'string' || isSymbol(value)) {
        return value;
      }
      var result = (value + '');
      return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
    }

    /**
     * Converts `func` to its source code.
     *
     * @private
     * @param {Function} func The function to process.
     * @returns {string} Returns the source code.
     */
    function toSource(func) {
      if (func != null) {
        try {
          return funcToString.call(func);
        } catch (e) {}
        try {
          return (func + '');
        } catch (e) {}
      }
      return '';
    }

    /**
     * Creates a clone of `wrapper`.
     *
     * @private
     * @param {Object} wrapper The wrapper to clone.
     * @returns {Object} Returns the cloned wrapper.
     */
    function wrapperClone(wrapper) {
      if (wrapper instanceof LazyWrapper) {
        return wrapper.clone();
      }
      var result = new LodashWrapper(wrapper.__wrapped__, wrapper.__chain__);
      result.__actions__ = copyArray(wrapper.__actions__);
      result.__index__  = wrapper.__index__;
      result.__values__ = wrapper.__values__;
      return result;
    }

    /*------------------------------------------------------------------------*/

    /**
     * Creates an array of elements split into groups the length of `size`.
     * If `array` can't be split evenly, the final chunk will be the remaining
     * elements.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to process.
     * @param {number} [size=1] The length of each chunk
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the new array of chunks.
     * @example
     *
     * _.chunk(['a', 'b', 'c', 'd'], 2);
     * // => [['a', 'b'], ['c', 'd']]
     *
     * _.chunk(['a', 'b', 'c', 'd'], 3);
     * // => [['a', 'b', 'c'], ['d']]
     */
    function chunk(array, size, guard) {
      if ((guard ? isIterateeCall(array, size, guard) : size === undefined)) {
        size = 1;
      } else {
        size = nativeMax(toInteger(size), 0);
      }
      var length = array ? array.length : 0;
      if (!length || size < 1) {
        return [];
      }
      var index = 0,
          resIndex = 0,
          result = Array(nativeCeil(length / size));

      while (index < length) {
        result[resIndex++] = baseSlice(array, index, (index += size));
      }
      return result;
    }

    /**
     * Creates an array with all falsey values removed. The values `false`, `null`,
     * `0`, `""`, `undefined`, and `NaN` are falsey.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to compact.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * _.compact([0, 1, false, 2, '', 3]);
     * // => [1, 2, 3]
     */
    function compact(array) {
      var index = -1,
          length = array ? array.length : 0,
          resIndex = 0,
          result = [];

      while (++index < length) {
        var value = array[index];
        if (value) {
          result[resIndex++] = value;
        }
      }
      return result;
    }

    /**
     * Creates a new array concatenating `array` with any additional arrays
     * and/or values.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to concatenate.
     * @param {...*} [values] The values to concatenate.
     * @returns {Array} Returns the new concatenated array.
     * @example
     *
     * var array = [1];
     * var other = _.concat(array, 2, [3], [[4]]);
     *
     * console.log(other);
     * // => [1, 2, 3, [4]]
     *
     * console.log(array);
     * // => [1]
     */
    function concat() {
      var length = arguments.length,
          args = Array(length ? length - 1 : 0),
          array = arguments[0],
          index = length;

      while (index--) {
        args[index - 1] = arguments[index];
      }
      return length
        ? arrayPush(isArray(array) ? copyArray(array) : [array], baseFlatten(args, 1))
        : [];
    }

    /**
     * Creates an array of unique `array` values not included in the other given
     * arrays using [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
     * for equality comparisons. The order of result values is determined by the
     * order they occur in the first array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...Array} [values] The values to exclude.
     * @returns {Array} Returns the new array of filtered values.
     * @see _.without, _.xor
     * @example
     *
     * _.difference([3, 2, 1], [4, 2]);
     * // => [3, 1]
     */
    var difference = rest(function(array, values) {
      return isArrayLikeObject(array)
        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true))
        : [];
    });

    /**
     * This method is like `_.difference` except that it accepts `iteratee` which
     * is invoked for each element of `array` and `values` to generate the criterion
     * by which they're compared. Result values are chosen from the first array.
     * The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...Array} [values] The values to exclude.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * _.differenceBy([3.1, 2.2, 1.3], [4.4, 2.5], Math.floor);
     * // => [3.1, 1.3]
     *
     * // The `_.property` iteratee shorthand.
     * _.differenceBy([{ 'x': 2 }, { 'x': 1 }], [{ 'x': 1 }], 'x');
     * // => [{ 'x': 2 }]
     */
    var differenceBy = rest(function(array, values) {
      var iteratee = last(values);
      if (isArrayLikeObject(iteratee)) {
        iteratee = undefined;
      }
      return isArrayLikeObject(array)
        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true), getIteratee(iteratee))
        : [];
    });

    /**
     * This method is like `_.difference` except that it accepts `comparator`
     * which is invoked to compare elements of `array` to `values`. Result values
     * are chosen from the first array. The comparator is invoked with two arguments:
     * (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...Array} [values] The values to exclude.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     *
     * _.differenceWith(objects, [{ 'x': 1, 'y': 2 }], _.isEqual);
     * // => [{ 'x': 2, 'y': 1 }]
     */
    var differenceWith = rest(function(array, values) {
      var comparator = last(values);
      if (isArrayLikeObject(comparator)) {
        comparator = undefined;
      }
      return isArrayLikeObject(array)
        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true), undefined, comparator)
        : [];
    });

    /**
     * Creates a slice of `array` with `n` elements dropped from the beginning.
     *
     * @static
     * @memberOf _
     * @since 0.5.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to drop.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.drop([1, 2, 3]);
     * // => [2, 3]
     *
     * _.drop([1, 2, 3], 2);
     * // => [3]
     *
     * _.drop([1, 2, 3], 5);
     * // => []
     *
     * _.drop([1, 2, 3], 0);
     * // => [1, 2, 3]
     */
    function drop(array, n, guard) {
      var length = array ? array.length : 0;
      if (!length) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      return baseSlice(array, n < 0 ? 0 : n, length);
    }

    /**
     * Creates a slice of `array` with `n` elements dropped from the end.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to drop.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.dropRight([1, 2, 3]);
     * // => [1, 2]
     *
     * _.dropRight([1, 2, 3], 2);
     * // => [1]
     *
     * _.dropRight([1, 2, 3], 5);
     * // => []
     *
     * _.dropRight([1, 2, 3], 0);
     * // => [1, 2, 3]
     */
    function dropRight(array, n, guard) {
      var length = array ? array.length : 0;
      if (!length) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      n = length - n;
      return baseSlice(array, 0, n < 0 ? 0 : n);
    }

    /**
     * Creates a slice of `array` excluding elements dropped from the end.
     * Elements are dropped until `predicate` returns falsey. The predicate is
     * invoked with three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': true },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': false }
     * ];
     *
     * _.dropRightWhile(users, function(o) { return !o.active; });
     * // => objects for ['barney']
     *
     * // The `_.matches` iteratee shorthand.
     * _.dropRightWhile(users, { 'user': 'pebbles', 'active': false });
     * // => objects for ['barney', 'fred']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.dropRightWhile(users, ['active', false]);
     * // => objects for ['barney']
     *
     * // The `_.property` iteratee shorthand.
     * _.dropRightWhile(users, 'active');
     * // => objects for ['barney', 'fred', 'pebbles']
     */
    function dropRightWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3), true, true)
        : [];
    }

    /**
     * Creates a slice of `array` excluding elements dropped from the beginning.
     * Elements are dropped until `predicate` returns falsey. The predicate is
     * invoked with three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': false },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': true }
     * ];
     *
     * _.dropWhile(users, function(o) { return !o.active; });
     * // => objects for ['pebbles']
     *
     * // The `_.matches` iteratee shorthand.
     * _.dropWhile(users, { 'user': 'barney', 'active': false });
     * // => objects for ['fred', 'pebbles']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.dropWhile(users, ['active', false]);
     * // => objects for ['pebbles']
     *
     * // The `_.property` iteratee shorthand.
     * _.dropWhile(users, 'active');
     * // => objects for ['barney', 'fred', 'pebbles']
     */
    function dropWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3), true)
        : [];
    }

    /**
     * Fills elements of `array` with `value` from `start` up to, but not
     * including, `end`.
     *
     * **Note:** This method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 3.2.0
     * @category Array
     * @param {Array} array The array to fill.
     * @param {*} value The value to fill `array` with.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [1, 2, 3];
     *
     * _.fill(array, 'a');
     * console.log(array);
     * // => ['a', 'a', 'a']
     *
     * _.fill(Array(3), 2);
     * // => [2, 2, 2]
     *
     * _.fill([4, 6, 8, 10], '*', 1, 3);
     * // => [4, '*', '*', 10]
     */
    function fill(array, value, start, end) {
      var length = array ? array.length : 0;
      if (!length) {
        return [];
      }
      if (start && typeof start != 'number' && isIterateeCall(array, value, start)) {
        start = 0;
        end = length;
      }
      return baseFill(array, value, start, end);
    }

    /**
     * This method is like `_.find` except that it returns the index of the first
     * element `predicate` returns truthy for instead of the element itself.
     *
     * @static
     * @memberOf _
     * @since 1.1.0
     * @category Array
     * @param {Array} array The array to search.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {number} Returns the index of the found element, else `-1`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': false },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': true }
     * ];
     *
     * _.findIndex(users, function(o) { return o.user == 'barney'; });
     * // => 0
     *
     * // The `_.matches` iteratee shorthand.
     * _.findIndex(users, { 'user': 'fred', 'active': false });
     * // => 1
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findIndex(users, ['active', false]);
     * // => 0
     *
     * // The `_.property` iteratee shorthand.
     * _.findIndex(users, 'active');
     * // => 2
     */
    function findIndex(array, predicate) {
      return (array && array.length)
        ? baseFindIndex(array, getIteratee(predicate, 3))
        : -1;
    }

    /**
     * This method is like `_.findIndex` except that it iterates over elements
     * of `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Array
     * @param {Array} array The array to search.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {number} Returns the index of the found element, else `-1`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': true },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': false }
     * ];
     *
     * _.findLastIndex(users, function(o) { return o.user == 'pebbles'; });
     * // => 2
     *
     * // The `_.matches` iteratee shorthand.
     * _.findLastIndex(users, { 'user': 'barney', 'active': true });
     * // => 0
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findLastIndex(users, ['active', false]);
     * // => 2
     *
     * // The `_.property` iteratee shorthand.
     * _.findLastIndex(users, 'active');
     * // => 0
     */
    function findLastIndex(array, predicate) {
      return (array && array.length)
        ? baseFindIndex(array, getIteratee(predicate, 3), true)
        : -1;
    }

    /**
     * Flattens `array` a single level deep.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to flatten.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * _.flatten([1, [2, [3, [4]], 5]]);
     * // => [1, 2, [3, [4]], 5]
     */
    function flatten(array) {
      var length = array ? array.length : 0;
      return length ? baseFlatten(array, 1) : [];
    }

    /**
     * Recursively flattens `array`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to flatten.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * _.flattenDeep([1, [2, [3, [4]], 5]]);
     * // => [1, 2, 3, 4, 5]
     */
    function flattenDeep(array) {
      var length = array ? array.length : 0;
      return length ? baseFlatten(array, INFINITY) : [];
    }

    /**
     * Recursively flatten `array` up to `depth` times.
     *
     * @static
     * @memberOf _
     * @since 4.4.0
     * @category Array
     * @param {Array} array The array to flatten.
     * @param {number} [depth=1] The maximum recursion depth.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * var array = [1, [2, [3, [4]], 5]];
     *
     * _.flattenDepth(array, 1);
     * // => [1, 2, [3, [4]], 5]
     *
     * _.flattenDepth(array, 2);
     * // => [1, 2, 3, [4], 5]
     */
    function flattenDepth(array, depth) {
      var length = array ? array.length : 0;
      if (!length) {
        return [];
      }
      depth = depth === undefined ? 1 : toInteger(depth);
      return baseFlatten(array, depth);
    }

    /**
     * The inverse of `_.toPairs`; this method returns an object composed
     * from key-value `pairs`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} pairs The key-value pairs.
     * @returns {Object} Returns the new object.
     * @example
     *
     * _.fromPairs([['fred', 30], ['barney', 40]]);
     * // => { 'fred': 30, 'barney': 40 }
     */
    function fromPairs(pairs) {
      var index = -1,
          length = pairs ? pairs.length : 0,
          result = {};

      while (++index < length) {
        var pair = pairs[index];
        result[pair[0]] = pair[1];
      }
      return result;
    }

    /**
     * Gets the first element of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @alias first
     * @category Array
     * @param {Array} array The array to query.
     * @returns {*} Returns the first element of `array`.
     * @example
     *
     * _.head([1, 2, 3]);
     * // => 1
     *
     * _.head([]);
     * // => undefined
     */
    function head(array) {
      return (array && array.length) ? array[0] : undefined;
    }

    /**
     * Gets the index at which the first occurrence of `value` is found in `array`
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
     * for equality comparisons. If `fromIndex` is negative, it's used as the
     * offset from the end of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to search.
     * @param {*} value The value to search for.
     * @param {number} [fromIndex=0] The index to search from.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.indexOf([1, 2, 1, 2], 2);
     * // => 1
     *
     * // Search from the `fromIndex`.
     * _.indexOf([1, 2, 1, 2], 2, 2);
     * // => 3
     */
    function indexOf(array, value, fromIndex) {
      var length = array ? array.length : 0;
      if (!length) {
        return -1;
      }
      fromIndex = toInteger(fromIndex);
      if (fromIndex < 0) {
        fromIndex = nativeMax(length + fromIndex, 0);
      }
      return baseIndexOf(array, value, fromIndex);
    }

    /**
     * Gets all but the last element of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to query.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.initial([1, 2, 3]);
     * // => [1, 2]
     */
    function initial(array) {
      return dropRight(array, 1);
    }

    /**
     * Creates an array of unique values that are included in all given arrays
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
     * for equality comparisons. The order of result values is determined by the
     * order they occur in the first array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @returns {Array} Returns the new array of intersecting values.
     * @example
     *
     * _.intersection([2, 1], [4, 2], [1, 2]);
     * // => [2]
     */
    var intersection = rest(function(arrays) {
      var mapped = arrayMap(arrays, castArrayLikeObject);
      return (mapped.length && mapped[0] === arrays[0])
        ? baseIntersection(mapped)
        : [];
    });

    /**
     * This method is like `_.intersection` except that it accepts `iteratee`
     * which is invoked for each element of each `arrays` to generate the criterion
     * by which they're compared. Result values are chosen from the first array.
     * The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {Array} Returns the new array of intersecting values.
     * @example
     *
     * _.intersectionBy([2.1, 1.2], [4.3, 2.4], Math.floor);
     * // => [2.1]
     *
     * // The `_.property` iteratee shorthand.
     * _.intersectionBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 1 }]
     */
    var intersectionBy = rest(function(arrays) {
      var iteratee = last(arrays),
          mapped = arrayMap(arrays, castArrayLikeObject);

      if (iteratee === last(mapped)) {
        iteratee = undefined;
      } else {
        mapped.pop();
      }
      return (mapped.length && mapped[0] === arrays[0])
        ? baseIntersection(mapped, getIteratee(iteratee))
        : [];
    });

    /**
     * This method is like `_.intersection` except that it accepts `comparator`
     * which is invoked to compare elements of `arrays`. Result values are chosen
     * from the first array. The comparator is invoked with two arguments:
     * (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of intersecting values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.intersectionWith(objects, others, _.isEqual);
     * // => [{ 'x': 1, 'y': 2 }]
     */
    var intersectionWith = rest(function(arrays) {
      var comparator = last(arrays),
          mapped = arrayMap(arrays, castArrayLikeObject);

      if (comparator === last(mapped)) {
        comparator = undefined;
      } else {
        mapped.pop();
      }
      return (mapped.length && mapped[0] === arrays[0])
        ? baseIntersection(mapped, undefined, comparator)
        : [];
    });

    /**
     * Converts all elements in `array` into a string separated by `separator`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to convert.
     * @param {string} [separator=','] The element separator.
     * @returns {string} Returns the joined string.
     * @example
     *
     * _.join(['a', 'b', 'c'], '~');
     * // => 'a~b~c'
     */
    function join(array, separator) {
      return array ? nativeJoin.call(array, separator) : '';
    }

    /**
     * Gets the last element of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to query.
     * @returns {*} Returns the last element of `array`.
     * @example
     *
     * _.last([1, 2, 3]);
     * // => 3
     */
    function last(array) {
      var length = array ? array.length : 0;
      return length ? array[length - 1] : undefined;
    }

    /**
     * This method is like `_.indexOf` except that it iterates over elements of
     * `array` from right to left.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to search.
     * @param {*} value The value to search for.
     * @param {number} [fromIndex=array.length-1] The index to search from.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.lastIndexOf([1, 2, 1, 2], 2);
     * // => 3
     *
     * // Search from the `fromIndex`.
     * _.lastIndexOf([1, 2, 1, 2], 2, 2);
     * // => 1
     */
    function lastIndexOf(array, value, fromIndex) {
      var length = array ? array.length : 0;
      if (!length) {
        return -1;
      }
      var index = length;
      if (fromIndex !== undefined) {
        index = toInteger(fromIndex);
        index = (
          index < 0
            ? nativeMax(length + index, 0)
            : nativeMin(index, length - 1)
        ) + 1;
      }
      if (value !== value) {
        return indexOfNaN(array, index, true);
      }
      while (index--) {
        if (array[index] === value) {
          return index;
        }
      }
      return -1;
    }

    /**
     * Gets the element at `n` index of `array`. If `n` is negative, the nth
     * element from the end is returned.
     *
     * @static
     * @memberOf _
     * @since 4.11.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=0] The index of the element to return.
     * @returns {*} Returns the nth element of `array`.
     * @example
     *
     * var array = ['a', 'b', 'c', 'd'];
     *
     * _.nth(array, 1);
     * // => 'b'
     *
     * _.nth(array, -2);
     * // => 'c';
     */
    function nth(array, n) {
      return (array && array.length) ? baseNth(array, toInteger(n)) : undefined;
    }

    /**
     * Removes all given values from `array` using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * **Note:** Unlike `_.without`, this method mutates `array`. Use `_.remove`
     * to remove elements from an array by predicate.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {...*} [values] The values to remove.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [1, 2, 3, 1, 2, 3];
     *
     * _.pull(array, 2, 3);
     * console.log(array);
     * // => [1, 1]
     */
    var pull = rest(pullAll);

    /**
     * This method is like `_.pull` except that it accepts an array of values to remove.
     *
     * **Note:** Unlike `_.difference`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [1, 2, 3, 1, 2, 3];
     *
     * _.pullAll(array, [2, 3]);
     * console.log(array);
     * // => [1, 1]
     */
    function pullAll(array, values) {
      return (array && array.length && values && values.length)
        ? basePullAll(array, values)
        : array;
    }

    /**
     * This method is like `_.pullAll` except that it accepts `iteratee` which is
     * invoked for each element of `array` and `values` to generate the criterion
     * by which they're compared. The iteratee is invoked with one argument: (value).
     *
     * **Note:** Unlike `_.differenceBy`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [{ 'x': 1 }, { 'x': 2 }, { 'x': 3 }, { 'x': 1 }];
     *
     * _.pullAllBy(array, [{ 'x': 1 }, { 'x': 3 }], 'x');
     * console.log(array);
     * // => [{ 'x': 2 }]
     */
    function pullAllBy(array, values, iteratee) {
      return (array && array.length && values && values.length)
        ? basePullAll(array, values, getIteratee(iteratee))
        : array;
    }

    /**
     * This method is like `_.pullAll` except that it accepts `comparator` which
     * is invoked to compare elements of `array` to `values`. The comparator is
     * invoked with two arguments: (arrVal, othVal).
     *
     * **Note:** Unlike `_.differenceWith`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 4.6.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [{ 'x': 1, 'y': 2 }, { 'x': 3, 'y': 4 }, { 'x': 5, 'y': 6 }];
     *
     * _.pullAllWith(array, [{ 'x': 3, 'y': 4 }], _.isEqual);
     * console.log(array);
     * // => [{ 'x': 1, 'y': 2 }, { 'x': 5, 'y': 6 }]
     */
    function pullAllWith(array, values, comparator) {
      return (array && array.length && values && values.length)
        ? basePullAll(array, values, undefined, comparator)
        : array;
    }

    /**
     * Removes elements from `array` corresponding to `indexes` and returns an
     * array of removed elements.
     *
     * **Note:** Unlike `_.at`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {...(number|number[])} [indexes] The indexes of elements to remove.
     * @returns {Array} Returns the new array of removed elements.
     * @example
     *
     * var array = [5, 10, 15, 20];
     * var evens = _.pullAt(array, 1, 3);
     *
     * console.log(array);
     * // => [5, 15]
     *
     * console.log(evens);
     * // => [10, 20]
     */
    var pullAt = rest(function(array, indexes) {
      indexes = baseFlatten(indexes, 1);

      var length = array ? array.length : 0,
          result = baseAt(array, indexes);

      basePullAt(array, arrayMap(indexes, function(index) {
        return isIndex(index, length) ? +index : index;
      }).sort(compareAscending));

      return result;
    });

    /**
     * Removes all elements from `array` that `predicate` returns truthy for
     * and returns an array of the removed elements. The predicate is invoked
     * with three arguments: (value, index, array).
     *
     * **Note:** Unlike `_.filter`, this method mutates `array`. Use `_.pull`
     * to pull elements from an array by value.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the new array of removed elements.
     * @example
     *
     * var array = [1, 2, 3, 4];
     * var evens = _.remove(array, function(n) {
     *   return n % 2 == 0;
     * });
     *
     * console.log(array);
     * // => [1, 3]
     *
     * console.log(evens);
     * // => [2, 4]
     */
    function remove(array, predicate) {
      var result = [];
      if (!(array && array.length)) {
        return result;
      }
      var index = -1,
          indexes = [],
          length = array.length;

      predicate = getIteratee(predicate, 3);
      while (++index < length) {
        var value = array[index];
        if (predicate(value, index, array)) {
          result.push(value);
          indexes.push(index);
        }
      }
      basePullAt(array, indexes);
      return result;
    }

    /**
     * Reverses `array` so that the first element becomes the last, the second
     * element becomes the second to last, and so on.
     *
     * **Note:** This method mutates `array` and is based on
     * [`Array#reverse`](https://mdn.io/Array/reverse).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [1, 2, 3];
     *
     * _.reverse(array);
     * // => [3, 2, 1]
     *
     * console.log(array);
     * // => [3, 2, 1]
     */
    function reverse(array) {
      return array ? nativeReverse.call(array) : array;
    }

    /**
     * Creates a slice of `array` from `start` up to, but not including, `end`.
     *
     * **Note:** This method is used instead of
     * [`Array#slice`](https://mdn.io/Array/slice) to ensure dense arrays are
     * returned.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to slice.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns the slice of `array`.
     */
    function slice(array, start, end) {
      var length = array ? array.length : 0;
      if (!length) {
        return [];
      }
      if (end && typeof end != 'number' && isIterateeCall(array, start, end)) {
        start = 0;
        end = length;
      }
      else {
        start = start == null ? 0 : toInteger(start);
        end = end === undefined ? length : toInteger(end);
      }
      return baseSlice(array, start, end);
    }

    /**
     * Uses a binary search to determine the lowest index at which `value`
     * should be inserted into `array` in order to maintain its sort order.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * _.sortedIndex([30, 50], 40);
     * // => 1
     *
     * _.sortedIndex([4, 5], 4);
     * // => 0
     */
    function sortedIndex(array, value) {
      return baseSortedIndex(array, value);
    }

    /**
     * This method is like `_.sortedIndex` except that it accepts `iteratee`
     * which is invoked for `value` and each element of `array` to compute their
     * sort ranking. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * var dict = { 'thirty': 30, 'forty': 40, 'fifty': 50 };
     *
     * _.sortedIndexBy(['thirty', 'fifty'], 'forty', _.propertyOf(dict));
     * // => 1
     *
     * // The `_.property` iteratee shorthand.
     * _.sortedIndexBy([{ 'x': 4 }, { 'x': 5 }], { 'x': 4 }, 'x');
     * // => 0
     */
    function sortedIndexBy(array, value, iteratee) {
      return baseSortedIndexBy(array, value, getIteratee(iteratee));
    }

    /**
     * This method is like `_.indexOf` except that it performs a binary
     * search on a sorted `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to search.
     * @param {*} value The value to search for.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.sortedIndexOf([1, 1, 2, 2], 2);
     * // => 2
     */
    function sortedIndexOf(array, value) {
      var length = array ? array.length : 0;
      if (length) {
        var index = baseSortedIndex(array, value);
        if (index < length && eq(array[index], value)) {
          return index;
        }
      }
      return -1;
    }

    /**
     * This method is like `_.sortedIndex` except that it returns the highest
     * index at which `value` should be inserted into `array` in order to
     * maintain its sort order.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * _.sortedLastIndex([4, 5], 4);
     * // => 1
     */
    function sortedLastIndex(array, value) {
      return baseSortedIndex(array, value, true);
    }

    /**
     * This method is like `_.sortedLastIndex` except that it accepts `iteratee`
     * which is invoked for `value` and each element of `array` to compute their
     * sort ranking. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * // The `_.property` iteratee shorthand.
     * _.sortedLastIndexBy([{ 'x': 4 }, { 'x': 5 }], { 'x': 4 }, 'x');
     * // => 1
     */
    function sortedLastIndexBy(array, value, iteratee) {
      return baseSortedIndexBy(array, value, getIteratee(iteratee), true);
    }

    /**
     * This method is like `_.lastIndexOf` except that it performs a binary
     * search on a sorted `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to search.
     * @param {*} value The value to search for.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.sortedLastIndexOf([1, 1, 2, 2], 2);
     * // => 3
     */
    function sortedLastIndexOf(array, value) {
      var length = array ? array.length : 0;
      if (length) {
        var index = baseSortedIndex(array, value, true) - 1;
        if (eq(array[index], value)) {
          return index;
        }
      }
      return -1;
    }

    /**
     * This method is like `_.uniq` except that it's designed and optimized
     * for sorted arrays.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.sortedUniq([1, 1, 2]);
     * // => [1, 2]
     */
    function sortedUniq(array) {
      return (array && array.length)
        ? baseSortedUniq(array)
        : [];
    }

    /**
     * This method is like `_.uniqBy` except that it's designed and optimized
     * for sorted arrays.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.sortedUniqBy([1.1, 1.2, 2.3, 2.4], Math.floor);
     * // => [1.1, 2.3]
     */
    function sortedUniqBy(array, iteratee) {
      return (array && array.length)
        ? baseSortedUniq(array, getIteratee(iteratee))
        : [];
    }

    /**
     * Gets all but the first element of `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.tail([1, 2, 3]);
     * // => [2, 3]
     */
    function tail(array) {
      return drop(array, 1);
    }

    /**
     * Creates a slice of `array` with `n` elements taken from the beginning.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to take.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.take([1, 2, 3]);
     * // => [1]
     *
     * _.take([1, 2, 3], 2);
     * // => [1, 2]
     *
     * _.take([1, 2, 3], 5);
     * // => [1, 2, 3]
     *
     * _.take([1, 2, 3], 0);
     * // => []
     */
    function take(array, n, guard) {
      if (!(array && array.length)) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      return baseSlice(array, 0, n < 0 ? 0 : n);
    }

    /**
     * Creates a slice of `array` with `n` elements taken from the end.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to take.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.takeRight([1, 2, 3]);
     * // => [3]
     *
     * _.takeRight([1, 2, 3], 2);
     * // => [2, 3]
     *
     * _.takeRight([1, 2, 3], 5);
     * // => [1, 2, 3]
     *
     * _.takeRight([1, 2, 3], 0);
     * // => []
     */
    function takeRight(array, n, guard) {
      var length = array ? array.length : 0;
      if (!length) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      n = length - n;
      return baseSlice(array, n < 0 ? 0 : n, length);
    }

    /**
     * Creates a slice of `array` with elements taken from the end. Elements are
     * taken until `predicate` returns falsey. The predicate is invoked with
     * three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': true },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': false }
     * ];
     *
     * _.takeRightWhile(users, function(o) { return !o.active; });
     * // => objects for ['fred', 'pebbles']
     *
     * // The `_.matches` iteratee shorthand.
     * _.takeRightWhile(users, { 'user': 'pebbles', 'active': false });
     * // => objects for ['pebbles']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.takeRightWhile(users, ['active', false]);
     * // => objects for ['fred', 'pebbles']
     *
     * // The `_.property` iteratee shorthand.
     * _.takeRightWhile(users, 'active');
     * // => []
     */
    function takeRightWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3), false, true)
        : [];
    }

    /**
     * Creates a slice of `array` with elements taken from the beginning. Elements
     * are taken until `predicate` returns falsey. The predicate is invoked with
     * three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': false },
     *   { 'user': 'fred',    'active': false},
     *   { 'user': 'pebbles', 'active': true }
     * ];
     *
     * _.takeWhile(users, function(o) { return !o.active; });
     * // => objects for ['barney', 'fred']
     *
     * // The `_.matches` iteratee shorthand.
     * _.takeWhile(users, { 'user': 'barney', 'active': false });
     * // => objects for ['barney']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.takeWhile(users, ['active', false]);
     * // => objects for ['barney', 'fred']
     *
     * // The `_.property` iteratee shorthand.
     * _.takeWhile(users, 'active');
     * // => []
     */
    function takeWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3))
        : [];
    }

    /**
     * Creates an array of unique values, in order, from all given arrays using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @returns {Array} Returns the new array of combined values.
     * @example
     *
     * _.union([2, 1], [4, 2], [1, 2]);
     * // => [2, 1, 4]
     */
    var union = rest(function(arrays) {
      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true));
    });

    /**
     * This method is like `_.union` except that it accepts `iteratee` which is
     * invoked for each element of each `arrays` to generate the criterion by
     * which uniqueness is computed. The iteratee is invoked with one argument:
     * (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {Array} Returns the new array of combined values.
     * @example
     *
     * _.unionBy([2.1, 1.2], [4.3, 2.4], Math.floor);
     * // => [2.1, 1.2, 4.3]
     *
     * // The `_.property` iteratee shorthand.
     * _.unionBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 1 }, { 'x': 2 }]
     */
    var unionBy = rest(function(arrays) {
      var iteratee = last(arrays);
      if (isArrayLikeObject(iteratee)) {
        iteratee = undefined;
      }
      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true), getIteratee(iteratee));
    });

    /**
     * This method is like `_.union` except that it accepts `comparator` which
     * is invoked to compare elements of `arrays`. The comparator is invoked
     * with two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of combined values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.unionWith(objects, others, _.isEqual);
     * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }, { 'x': 1, 'y': 1 }]
     */
    var unionWith = rest(function(arrays) {
      var comparator = last(arrays);
      if (isArrayLikeObject(comparator)) {
        comparator = undefined;
      }
      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true), undefined, comparator);
    });

    /**
     * Creates a duplicate-free version of an array, using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
     * for equality comparisons, in which only the first occurrence of each
     * element is kept.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.uniq([2, 1, 2]);
     * // => [2, 1]
     */
    function uniq(array) {
      return (array && array.length)
        ? baseUniq(array)
        : [];
    }

    /**
     * This method is like `_.uniq` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the criterion by which
     * uniqueness is computed. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.uniqBy([2.1, 1.2, 2.3], Math.floor);
     * // => [2.1, 1.2]
     *
     * // The `_.property` iteratee shorthand.
     * _.uniqBy([{ 'x': 1 }, { 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 1 }, { 'x': 2 }]
     */
    function uniqBy(array, iteratee) {
      return (array && array.length)
        ? baseUniq(array, getIteratee(iteratee))
        : [];
    }

    /**
     * This method is like `_.uniq` except that it accepts `comparator` which
     * is invoked to compare elements of `array`. The comparator is invoked with
     * two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 },  { 'x': 1, 'y': 2 }];
     *
     * _.uniqWith(objects, _.isEqual);
     * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }]
     */
    function uniqWith(array, comparator) {
      return (array && array.length)
        ? baseUniq(array, undefined, comparator)
        : [];
    }

    /**
     * This method is like `_.zip` except that it accepts an array of grouped
     * elements and creates an array regrouping the elements to their pre-zip
     * configuration.
     *
     * @static
     * @memberOf _
     * @since 1.2.0
     * @category Array
     * @param {Array} array The array of grouped elements to process.
     * @returns {Array} Returns the new array of regrouped elements.
     * @example
     *
     * var zipped = _.zip(['fred', 'barney'], [30, 40], [true, false]);
     * // => [['fred', 30, true], ['barney', 40, false]]
     *
     * _.unzip(zipped);
     * // => [['fred', 'barney'], [30, 40], [true, false]]
     */
    function unzip(array) {
      if (!(array && array.length)) {
        return [];
      }
      var length = 0;
      array = arrayFilter(array, function(group) {
        if (isArrayLikeObject(group)) {
          length = nativeMax(group.length, length);
          return true;
        }
      });
      return baseTimes(length, function(index) {
        return arrayMap(array, baseProperty(index));
      });
    }

    /**
     * This method is like `_.unzip` except that it accepts `iteratee` to specify
     * how regrouped values should be combined. The iteratee is invoked with the
     * elements of each group: (...group).
     *
     * @static
     * @memberOf _
     * @since 3.8.0
     * @category Array
     * @param {Array} array The array of grouped elements to process.
     * @param {Function} [iteratee=_.identity] The function to combine
     *  regrouped values.
     * @returns {Array} Returns the new array of regrouped elements.
     * @example
     *
     * var zipped = _.zip([1, 2], [10, 20], [100, 200]);
     * // => [[1, 10, 100], [2, 20, 200]]
     *
     * _.unzipWith(zipped, _.add);
     * // => [3, 30, 300]
     */
    function unzipWith(array, iteratee) {
      if (!(array && array.length)) {
        return [];
      }
      var result = unzip(array);
      if (iteratee == null) {
        return result;
      }
      return arrayMap(result, function(group) {
        return apply(iteratee, undefined, group);
      });
    }

    /**
     * Creates an array excluding all given values using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...*} [values] The values to exclude.
     * @returns {Array} Returns the new array of filtered values.
     * @see _.difference, _.xor
     * @example
     *
     * _.without([1, 2, 1, 3], 1, 2);
     * // => [3]
     */
    var without = rest(function(array, values) {
      return isArrayLikeObject(array)
        ? baseDifference(array, values)
        : [];
    });

    /**
     * Creates an array of unique values that is the
     * [symmetric difference](https://en.wikipedia.org/wiki/Symmetric_difference)
     * of the given arrays. The order of result values is determined by the order
     * they occur in the arrays.
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @returns {Array} Returns the new array of filtered values.
     * @see _.difference, _.without
     * @example
     *
     * _.xor([2, 1], [4, 2]);
     * // => [1, 4]
     */
    var xor = rest(function(arrays) {
      return baseXor(arrayFilter(arrays, isArrayLikeObject));
    });

    /**
     * This method is like `_.xor` except that it accepts `iteratee` which is
     * invoked for each element of each `arrays` to generate the criterion by
     * which by which they're compared. The iteratee is invoked with one argument:
     * (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * _.xorBy([2.1, 1.2], [4.3, 2.4], Math.floor);
     * // => [1.2, 4.3]
     *
     * // The `_.property` iteratee shorthand.
     * _.xorBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 2 }]
     */
    var xorBy = rest(function(arrays) {
      var iteratee = last(arrays);
      if (isArrayLikeObject(iteratee)) {
        iteratee = undefined;
      }
      return baseXor(arrayFilter(arrays, isArrayLikeObject), getIteratee(iteratee));
    });

    /**
     * This method is like `_.xor` except that it accepts `comparator` which is
     * invoked to compare elements of `arrays`. The comparator is invoked with
     * two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.xorWith(objects, others, _.isEqual);
     * // => [{ 'x': 2, 'y': 1 }, { 'x': 1, 'y': 1 }]
     */
    var xorWith = rest(function(arrays) {
      var comparator = last(arrays);
      if (isArrayLikeObject(comparator)) {
        comparator = undefined;
      }
      return baseXor(arrayFilter(arrays, isArrayLikeObject), undefined, comparator);
    });

    /**
     * Creates an array of grouped elements, the first of which contains the
     * first elements of the given arrays, the second of which contains the
     * second elements of the given arrays, and so on.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {...Array} [arrays] The arrays to process.
     * @returns {Array} Returns the new array of grouped elements.
     * @example
     *
     * _.zip(['fred', 'barney'], [30, 40], [true, false]);
     * // => [['fred', 30, true], ['barney', 40, false]]
     */
    var zip = rest(unzip);

    /**
     * This method is like `_.fromPairs` except that it accepts two arrays,
     * one of property identifiers and one of corresponding values.
     *
     * @static
     * @memberOf _
     * @since 0.4.0
     * @category Array
     * @param {Array} [props=[]] The property identifiers.
     * @param {Array} [values=[]] The property values.
     * @returns {Object} Returns the new object.
     * @example
     *
     * _.zipObject(['a', 'b'], [1, 2]);
     * // => { 'a': 1, 'b': 2 }
     */
    function zipObject(props, values) {
      return baseZipObject(props || [], values || [], assignValue);
    }

    /**
     * This method is like `_.zipObject` except that it supports property paths.
     *
     * @static
     * @memberOf _
     * @since 4.1.0
     * @category Array
     * @param {Array} [props=[]] The property identifiers.
     * @param {Array} [values=[]] The property values.
     * @returns {Object} Returns the new object.
     * @example
     *
     * _.zipObjectDeep(['a.b[0].c', 'a.b[1].d'], [1, 2]);
     * // => { 'a': { 'b': [{ 'c': 1 }, { 'd': 2 }] } }
     */
    function zipObjectDeep(props, values) {
      return baseZipObject(props || [], values || [], baseSet);
    }

    /**
     * This method is like `_.zip` except that it accepts `iteratee` to specify
     * how grouped values should be combined. The iteratee is invoked with the
     * elements of each group: (...group).
     *
     * @static
     * @memberOf _
     * @since 3.8.0
     * @category Array
     * @param {...Array} [arrays] The arrays to process.
     * @param {Function} [iteratee=_.identity] The function to combine grouped values.
     * @returns {Array} Returns the new array of grouped elements.
     * @example
     *
     * _.zipWith([1, 2], [10, 20], [100, 200], function(a, b, c) {
     *   return a + b + c;
     * });
     * // => [111, 222]
     */
    var zipWith = rest(function(arrays) {
      var length = arrays.length,
          iteratee = length > 1 ? arrays[length - 1] : undefined;

      iteratee = typeof iteratee == 'function' ? (arrays.pop(), iteratee) : undefined;
      return unzipWith(arrays, iteratee);
    });

    /*------------------------------------------------------------------------*/

    /**
     * Creates a `lodash` wrapper instance that wraps `value` with explicit method
     * chain sequences enabled. The result of such sequences must be unwrapped
     * with `_#value`.
     *
     * @static
     * @memberOf _
     * @since 1.3.0
     * @category Seq
     * @param {*} value The value to wrap.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'age': 36 },
     *   { 'user': 'fred',    'age': 40 },
     *   { 'user': 'pebbles', 'age': 1 }
     * ];
     *
     * var youngest = _
     *   .chain(users)
     *   .sortBy('age')
     *   .map(function(o) {
     *     return o.user + ' is ' + o.age;
     *   })
     *   .head()
     *   .value();
     * // => 'pebbles is 1'
     */
    function chain(value) {
      var result = lodash(value);
      result.__chain__ = true;
      return result;
    }

    /**
     * This method invokes `interceptor` and returns `value`. The interceptor
     * is invoked with one argument; (value). The purpose of this method is to
     * "tap into" a method chain sequence in order to modify intermediate results.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Seq
     * @param {*} value The value to provide to `interceptor`.
     * @param {Function} interceptor The function to invoke.
     * @returns {*} Returns `value`.
     * @example
     *
     * _([1, 2, 3])
     *  .tap(function(array) {
     *    // Mutate input array.
     *    array.pop();
     *  })
     *  .reverse()
     *  .value();
     * // => [2, 1]
     */
    function tap(value, interceptor) {
      interceptor(value);
      return value;
    }

    /**
     * This method is like `_.tap` except that it returns the result of `interceptor`.
     * The purpose of this method is to "pass thru" values replacing intermediate
     * results in a method chain sequence.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Seq
     * @param {*} value The value to provide to `interceptor`.
     * @param {Function} interceptor The function to invoke.
     * @returns {*} Returns the result of `interceptor`.
     * @example
     *
     * _('  abc  ')
     *  .chain()
     *  .trim()
     *  .thru(function(value) {
     *    return [value];
     *  })
     *  .value();
     * // => ['abc']
     */
    function thru(value, interceptor) {
      return interceptor(value);
    }

    /**
     * This method is the wrapper version of `_.at`.
     *
     * @name at
     * @memberOf _
     * @since 1.0.0
     * @category Seq
     * @param {...(string|string[])} [paths] The property paths of elements to pick.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }, 4] };
     *
     * _(object).at(['a[0].b.c', 'a[1]']).value();
     * // => [3, 4]
     *
     * _(['a', 'b', 'c']).at(0, 2).value();
     * // => ['a', 'c']
     */
    var wrapperAt = rest(function(paths) {
      paths = baseFlatten(paths, 1);
      var length = paths.length,
          start = length ? paths[0] : 0,
          value = this.__wrapped__,
          interceptor = function(object) { return baseAt(object, paths); };

      if (length > 1 || this.__actions__.length ||
          !(value instanceof LazyWrapper) || !isIndex(start)) {
        return this.thru(interceptor);
      }
      value = value.slice(start, +start + (length ? 1 : 0));
      value.__actions__.push({
        'func': thru,
        'args': [interceptor],
        'thisArg': undefined
      });
      return new LodashWrapper(value, this.__chain__).thru(function(array) {
        if (length && !array.length) {
          array.push(undefined);
        }
        return array;
      });
    });

    /**
     * Creates a `lodash` wrapper instance with explicit method chain sequences enabled.
     *
     * @name chain
     * @memberOf _
     * @since 0.1.0
     * @category Seq
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36 },
     *   { 'user': 'fred',   'age': 40 }
     * ];
     *
     * // A sequence without explicit chaining.
     * _(users).head();
     * // => { 'user': 'barney', 'age': 36 }
     *
     * // A sequence with explicit chaining.
     * _(users)
     *   .chain()
     *   .head()
     *   .pick('user')
     *   .value();
     * // => { 'user': 'barney' }
     */
    function wrapperChain() {
      return chain(this);
    }

    /**
     * Executes the chain sequence and returns the wrapped result.
     *
     * @name commit
     * @memberOf _
     * @since 3.2.0
     * @category Seq
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var array = [1, 2];
     * var wrapped = _(array).push(3);
     *
     * console.log(array);
     * // => [1, 2]
     *
     * wrapped = wrapped.commit();
     * console.log(array);
     * // => [1, 2, 3]
     *
     * wrapped.last();
     * // => 3
     *
     * console.log(array);
     * // => [1, 2, 3]
     */
    function wrapperCommit() {
      return new LodashWrapper(this.value(), this.__chain__);
    }

    /**
     * Gets the next value on a wrapped object following the
     * [iterator protocol](https://mdn.io/iteration_protocols#iterator).
     *
     * @name next
     * @memberOf _
     * @since 4.0.0
     * @category Seq
     * @returns {Object} Returns the next iterator value.
     * @example
     *
     * var wrapped = _([1, 2]);
     *
     * wrapped.next();
     * // => { 'done': false, 'value': 1 }
     *
     * wrapped.next();
     * // => { 'done': false, 'value': 2 }
     *
     * wrapped.next();
     * // => { 'done': true, 'value': undefined }
     */
    function wrapperNext() {
      if (this.__values__ === undefined) {
        this.__values__ = toArray(this.value());
      }
      var done = this.__index__ >= this.__values__.length,
          value = done ? undefined : this.__values__[this.__index__++];

      return { 'done': done, 'value': value };
    }

    /**
     * Enables the wrapper to be iterable.
     *
     * @name Symbol.iterator
     * @memberOf _
     * @since 4.0.0
     * @category Seq
     * @returns {Object} Returns the wrapper object.
     * @example
     *
     * var wrapped = _([1, 2]);
     *
     * wrapped[Symbol.iterator]() === wrapped;
     * // => true
     *
     * Array.from(wrapped);
     * // => [1, 2]
     */
    function wrapperToIterator() {
      return this;
    }

    /**
     * Creates a clone of the chain sequence planting `value` as the wrapped value.
     *
     * @name plant
     * @memberOf _
     * @since 3.2.0
     * @category Seq
     * @param {*} value The value to plant.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var wrapped = _([1, 2]).map(square);
     * var other = wrapped.plant([3, 4]);
     *
     * other.value();
     * // => [9, 16]
     *
     * wrapped.value();
     * // => [1, 4]
     */
    function wrapperPlant(value) {
      var result,
          parent = this;

      while (parent instanceof baseLodash) {
        var clone = wrapperClone(parent);
        clone.__index__ = 0;
        clone.__values__ = undefined;
        if (result) {
          previous.__wrapped__ = clone;
        } else {
          result = clone;
        }
        var previous = clone;
        parent = parent.__wrapped__;
      }
      previous.__wrapped__ = value;
      return result;
    }

    /**
     * This method is the wrapper version of `_.reverse`.
     *
     * **Note:** This method mutates the wrapped array.
     *
     * @name reverse
     * @memberOf _
     * @since 0.1.0
     * @category Seq
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var array = [1, 2, 3];
     *
     * _(array).reverse().value()
     * // => [3, 2, 1]
     *
     * console.log(array);
     * // => [3, 2, 1]
     */
    function wrapperReverse() {
      var value = this.__wrapped__;
      if (value instanceof LazyWrapper) {
        var wrapped = value;
        if (this.__actions__.length) {
          wrapped = new LazyWrapper(this);
        }
        wrapped = wrapped.reverse();
        wrapped.__actions__.push({
          'func': thru,
          'args': [reverse],
          'thisArg': undefined
        });
        return new LodashWrapper(wrapped, this.__chain__);
      }
      return this.thru(reverse);
    }

    /**
     * Executes the chain sequence to resolve the unwrapped value.
     *
     * @name value
     * @memberOf _
     * @since 0.1.0
     * @alias toJSON, valueOf
     * @category Seq
     * @returns {*} Returns the resolved unwrapped value.
     * @example
     *
     * _([1, 2, 3]).value();
     * // => [1, 2, 3]
     */
    function wrapperValue() {
      return baseWrapperValue(this.__wrapped__, this.__actions__);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Creates an object composed of keys generated from the results of running
     * each element of `collection` thru `iteratee`. The corresponding value of
     * each key is the number of times the key was returned by `iteratee`. The
     * iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 0.5.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee to transform keys.
     * @returns {Object} Returns the composed aggregate object.
     * @example
     *
     * _.countBy([6.1, 4.2, 6.3], Math.floor);
     * // => { '4': 1, '6': 2 }
     *
     * _.countBy(['one', 'two', 'three'], 'length');
     * // => { '3': 2, '5': 1 }
     */
    var countBy = createAggregator(function(result, value, key) {
      hasOwnProperty.call(result, key) ? ++result[key] : (result[key] = 1);
    });

    /**
     * Checks if `predicate` returns truthy for **all** elements of `collection`.
     * Iteration is stopped once `predicate` returns falsey. The predicate is
     * invoked with three arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {boolean} Returns `true` if all elements pass the predicate check,
     *  else `false`.
     * @example
     *
     * _.every([true, 1, null, 'yes'], Boolean);
     * // => false
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': false },
     *   { 'user': 'fred',   'age': 40, 'active': false }
     * ];
     *
     * // The `_.matches` iteratee shorthand.
     * _.every(users, { 'user': 'barney', 'active': false });
     * // => false
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.every(users, ['active', false]);
     * // => true
     *
     * // The `_.property` iteratee shorthand.
     * _.every(users, 'active');
     * // => false
     */
    function every(collection, predicate, guard) {
      var func = isArray(collection) ? arrayEvery : baseEvery;
      if (guard && isIterateeCall(collection, predicate, guard)) {
        predicate = undefined;
      }
      return func(collection, getIteratee(predicate, 3));
    }

    /**
     * Iterates over elements of `collection`, returning an array of all elements
     * `predicate` returns truthy for. The predicate is invoked with three
     * arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the new filtered array.
     * @see _.reject
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': true },
     *   { 'user': 'fred',   'age': 40, 'active': false }
     * ];
     *
     * _.filter(users, function(o) { return !o.active; });
     * // => objects for ['fred']
     *
     * // The `_.matches` iteratee shorthand.
     * _.filter(users, { 'age': 36, 'active': true });
     * // => objects for ['barney']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.filter(users, ['active', false]);
     * // => objects for ['fred']
     *
     * // The `_.property` iteratee shorthand.
     * _.filter(users, 'active');
     * // => objects for ['barney']
     */
    function filter(collection, predicate) {
      var func = isArray(collection) ? arrayFilter : baseFilter;
      return func(collection, getIteratee(predicate, 3));
    }

    /**
     * Iterates over elements of `collection`, returning the first element
     * `predicate` returns truthy for. The predicate is invoked with three
     * arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to search.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {*} Returns the matched element, else `undefined`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'age': 36, 'active': true },
     *   { 'user': 'fred',    'age': 40, 'active': false },
     *   { 'user': 'pebbles', 'age': 1,  'active': true }
     * ];
     *
     * _.find(users, function(o) { return o.age < 40; });
     * // => object for 'barney'
     *
     * // The `_.matches` iteratee shorthand.
     * _.find(users, { 'age': 1, 'active': true });
     * // => object for 'pebbles'
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.find(users, ['active', false]);
     * // => object for 'fred'
     *
     * // The `_.property` iteratee shorthand.
     * _.find(users, 'active');
     * // => object for 'barney'
     */
    function find(collection, predicate) {
      predicate = getIteratee(predicate, 3);
      if (isArray(collection)) {
        var index = baseFindIndex(collection, predicate);
        return index > -1 ? collection[index] : undefined;
      }
      return baseFind(collection, predicate, baseEach);
    }

    /**
     * This method is like `_.find` except that it iterates over elements of
     * `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to search.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {*} Returns the matched element, else `undefined`.
     * @example
     *
     * _.findLast([1, 2, 3, 4], function(n) {
     *   return n % 2 == 1;
     * });
     * // => 3
     */
    function findLast(collection, predicate) {
      predicate = getIteratee(predicate, 3);
      if (isArray(collection)) {
        var index = baseFindIndex(collection, predicate, true);
        return index > -1 ? collection[index] : undefined;
      }
      return baseFind(collection, predicate, baseEachRight);
    }

    /**
     * Creates a flattened array of values by running each element in `collection`
     * thru `iteratee` and flattening the mapped results. The iteratee is invoked
     * with three arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * function duplicate(n) {
     *   return [n, n];
     * }
     *
     * _.flatMap([1, 2], duplicate);
     * // => [1, 1, 2, 2]
     */
    function flatMap(collection, iteratee) {
      return baseFlatten(map(collection, iteratee), 1);
    }

    /**
     * This method is like `_.flatMap` except that it recursively flattens the
     * mapped results.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * function duplicate(n) {
     *   return [[[n, n]]];
     * }
     *
     * _.flatMapDeep([1, 2], duplicate);
     * // => [1, 1, 2, 2]
     */
    function flatMapDeep(collection, iteratee) {
      return baseFlatten(map(collection, iteratee), INFINITY);
    }

    /**
     * This method is like `_.flatMap` except that it recursively flattens the
     * mapped results up to `depth` times.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The function invoked per iteration.
     * @param {number} [depth=1] The maximum recursion depth.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * function duplicate(n) {
     *   return [[[n, n]]];
     * }
     *
     * _.flatMapDepth([1, 2], duplicate, 2);
     * // => [[1, 1], [2, 2]]
     */
    function flatMapDepth(collection, iteratee, depth) {
      depth = depth === undefined ? 1 : toInteger(depth);
      return baseFlatten(map(collection, iteratee), depth);
    }

    /**
     * Iterates over elements of `collection` and invokes `iteratee` for each element.
     * The iteratee is invoked with three arguments: (value, index|key, collection).
     * Iteratee functions may exit iteration early by explicitly returning `false`.
     *
     * **Note:** As with other "Collections" methods, objects with a "length"
     * property are iterated like arrays. To avoid this behavior use `_.forIn`
     * or `_.forOwn` for object iteration.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @alias each
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     * @see _.forEachRight
     * @example
     *
     * _([1, 2]).forEach(function(value) {
     *   console.log(value);
     * });
     * // => Logs `1` then `2`.
     *
     * _.forEach({ 'a': 1, 'b': 2 }, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'a' then 'b' (iteration order is not guaranteed).
     */
    function forEach(collection, iteratee) {
      var func = isArray(collection) ? arrayEach : baseEach;
      return func(collection, getIteratee(iteratee, 3));
    }

    /**
     * This method is like `_.forEach` except that it iterates over elements of
     * `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @alias eachRight
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     * @see _.forEach
     * @example
     *
     * _.forEachRight([1, 2], function(value) {
     *   console.log(value);
     * });
     * // => Logs `2` then `1`.
     */
    function forEachRight(collection, iteratee) {
      var func = isArray(collection) ? arrayEachRight : baseEachRight;
      return func(collection, getIteratee(iteratee, 3));
    }

    /**
     * Creates an object composed of keys generated from the results of running
     * each element of `collection` thru `iteratee`. The order of grouped values
     * is determined by the order they occur in `collection`. The corresponding
     * value of each key is an array of elements responsible for generating the
     * key. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee to transform keys.
     * @returns {Object} Returns the composed aggregate object.
     * @example
     *
     * _.groupBy([6.1, 4.2, 6.3], Math.floor);
     * // => { '4': [4.2], '6': [6.1, 6.3] }
     *
     * // The `_.property` iteratee shorthand.
     * _.groupBy(['one', 'two', 'three'], 'length');
     * // => { '3': ['one', 'two'], '5': ['three'] }
     */
    var groupBy = createAggregator(function(result, value, key) {
      if (hasOwnProperty.call(result, key)) {
        result[key].push(value);
      } else {
        result[key] = [value];
      }
    });

    /**
     * Checks if `value` is in `collection`. If `collection` is a string, it's
     * checked for a substring of `value`, otherwise
     * [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
     * is used for equality comparisons. If `fromIndex` is negative, it's used as
     * the offset from the end of `collection`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object|string} collection The collection to search.
     * @param {*} value The value to search for.
     * @param {number} [fromIndex=0] The index to search from.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
     * @returns {boolean} Returns `true` if `value` is found, else `false`.
     * @example
     *
     * _.includes([1, 2, 3], 1);
     * // => true
     *
     * _.includes([1, 2, 3], 1, 2);
     * // => false
     *
     * _.includes({ 'user': 'fred', 'age': 40 }, 'fred');
     * // => true
     *
     * _.includes('pebbles', 'eb');
     * // => true
     */
    function includes(collection, value, fromIndex, guard) {
      collection = isArrayLike(collection) ? collection : values(collection);
      fromIndex = (fromIndex && !guard) ? toInteger(fromIndex) : 0;

      var length = collection.length;
      if (fromIndex < 0) {
        fromIndex = nativeMax(length + fromIndex, 0);
      }
      return isString(collection)
        ? (fromIndex <= length && collection.indexOf(value, fromIndex) > -1)
        : (!!length && baseIndexOf(collection, value, fromIndex) > -1);
    }

    /**
     * Invokes the method at `path` of each element in `collection`, returning
     * an array of the results of each invoked method. Any additional arguments
     * are provided to each invoked method. If `methodName` is a function, it's
     * invoked for and `this` bound to, each element in `collection`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|string} path The path of the method to invoke or
     *  the function invoked per iteration.
     * @param {...*} [args] The arguments to invoke each method with.
     * @returns {Array} Returns the array of results.
     * @example
     *
     * _.invokeMap([[5, 1, 7], [3, 2, 1]], 'sort');
     * // => [[1, 5, 7], [1, 2, 3]]
     *
     * _.invokeMap([123, 456], String.prototype.split, '');
     * // => [['1', '2', '3'], ['4', '5', '6']]
     */
    var invokeMap = rest(function(collection, path, args) {
      var index = -1,
          isFunc = typeof path == 'function',
          isProp = isKey(path),
          result = isArrayLike(collection) ? Array(collection.length) : [];

      baseEach(collection, function(value) {
        var func = isFunc ? path : ((isProp && value != null) ? value[path] : undefined);
        result[++index] = func ? apply(func, value, args) : baseInvoke(value, path, args);
      });
      return result;
    });

    /**
     * Creates an object composed of keys generated from the results of running
     * each element of `collection` thru `iteratee`. The corresponding value of
     * each key is the last element responsible for generating the key. The
     * iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee to transform keys.
     * @returns {Object} Returns the composed aggregate object.
     * @example
     *
     * var array = [
     *   { 'dir': 'left', 'code': 97 },
     *   { 'dir': 'right', 'code': 100 }
     * ];
     *
     * _.keyBy(array, function(o) {
     *   return String.fromCharCode(o.code);
     * });
     * // => { 'a': { 'dir': 'left', 'code': 97 }, 'd': { 'dir': 'right', 'code': 100 } }
     *
     * _.keyBy(array, 'dir');
     * // => { 'left': { 'dir': 'left', 'code': 97 }, 'right': { 'dir': 'right', 'code': 100 } }
     */
    var keyBy = createAggregator(function(result, value, key) {
      result[key] = value;
    });

    /**
     * Creates an array of values by running each element in `collection` thru
     * `iteratee`. The iteratee is invoked with three arguments:
     * (value, index|key, collection).
     *
     * Many lodash methods are guarded to work as iteratees for methods like
     * `_.every`, `_.filter`, `_.map`, `_.mapValues`, `_.reject`, and `_.some`.
     *
     * The guarded methods are:
     * `ary`, `chunk`, `curry`, `curryRight`, `drop`, `dropRight`, `every`,
     * `fill`, `invert`, `parseInt`, `random`, `range`, `rangeRight`, `repeat`,
     * `sampleSize`, `slice`, `some`, `sortBy`, `split`, `take`, `takeRight`,
     * `template`, `trim`, `trimEnd`, `trimStart`, and `words`
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the new mapped array.
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * _.map([4, 8], square);
     * // => [16, 64]
     *
     * _.map({ 'a': 4, 'b': 8 }, square);
     * // => [16, 64] (iteration order is not guaranteed)
     *
     * var users = [
     *   { 'user': 'barney' },
     *   { 'user': 'fred' }
     * ];
     *
     * // The `_.property` iteratee shorthand.
     * _.map(users, 'user');
     * // => ['barney', 'fred']
     */
    function map(collection, iteratee) {
      var func = isArray(collection) ? arrayMap : baseMap;
      return func(collection, getIteratee(iteratee, 3));
    }

    /**
     * This method is like `_.sortBy` except that it allows specifying the sort
     * orders of the iteratees to sort by. If `orders` is unspecified, all values
     * are sorted in ascending order. Otherwise, specify an order of "desc" for
     * descending or "asc" for ascending sort order of corresponding values.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array[]|Function[]|Object[]|string[]} [iteratees=[_.identity]]
     *  The iteratees to sort by.
     * @param {string[]} [orders] The sort orders of `iteratees`.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
     * @returns {Array} Returns the new sorted array.
     * @example
     *
     * var users = [
     *   { 'user': 'fred',   'age': 48 },
     *   { 'user': 'barney', 'age': 34 },
     *   { 'user': 'fred',   'age': 40 },
     *   { 'user': 'barney', 'age': 36 }
     * ];
     *
     * // Sort by `user` in ascending order and by `age` in descending order.
     * _.orderBy(users, ['user', 'age'], ['asc', 'desc']);
     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 40]]
     */
    function orderBy(collection, iteratees, orders, guard) {
      if (collection == null) {
        return [];
      }
      if (!isArray(iteratees)) {
        iteratees = iteratees == null ? [] : [iteratees];
      }
      orders = guard ? undefined : orders;
      if (!isArray(orders)) {
        orders = orders == null ? [] : [orders];
      }
      return baseOrderBy(collection, iteratees, orders);
    }

    /**
     * Creates an array of elements split into two groups, the first of which
     * contains elements `predicate` returns truthy for, the second of which
     * contains elements `predicate` returns falsey for. The predicate is
     * invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the array of grouped elements.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'age': 36, 'active': false },
     *   { 'user': 'fred',    'age': 40, 'active': true },
     *   { 'user': 'pebbles', 'age': 1,  'active': false }
     * ];
     *
     * _.partition(users, function(o) { return o.active; });
     * // => objects for [['fred'], ['barney', 'pebbles']]
     *
     * // The `_.matches` iteratee shorthand.
     * _.partition(users, { 'age': 1, 'active': false });
     * // => objects for [['pebbles'], ['barney', 'fred']]
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.partition(users, ['active', false]);
     * // => objects for [['barney', 'pebbles'], ['fred']]
     *
     * // The `_.property` iteratee shorthand.
     * _.partition(users, 'active');
     * // => objects for [['fred'], ['barney', 'pebbles']]
     */
    var partition = createAggregator(function(result, value, key) {
      result[key ? 0 : 1].push(value);
    }, function() { return [[], []]; });

    /**
     * Reduces `collection` to a value which is the accumulated result of running
     * each element in `collection` thru `iteratee`, where each successive
     * invocation is supplied the return value of the previous. If `accumulator`
     * is not given, the first element of `collection` is used as the initial
     * value. The iteratee is invoked with four arguments:
     * (accumulator, value, index|key, collection).
     *
     * Many lodash methods are guarded to work as iteratees for methods like
     * `_.reduce`, `_.reduceRight`, and `_.transform`.
     *
     * The guarded methods are:
     * `assign`, `defaults`, `defaultsDeep`, `includes`, `merge`, `orderBy`,
     * and `sortBy`
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {*} [accumulator] The initial value.
     * @returns {*} Returns the accumulated value.
     * @see _.reduceRight
     * @example
     *
     * _.reduce([1, 2], function(sum, n) {
     *   return sum + n;
     * }, 0);
     * // => 3
     *
     * _.reduce({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
     *   (result[value] || (result[value] = [])).push(key);
     *   return result;
     * }, {});
     * // => { '1': ['a', 'c'], '2': ['b'] } (iteration order is not guaranteed)
     */
    function reduce(collection, iteratee, accumulator) {
      var func = isArray(collection) ? arrayReduce : baseReduce,
          initAccum = arguments.length < 3;

      return func(collection, getIteratee(iteratee, 4), accumulator, initAccum, baseEach);
    }

    /**
     * This method is like `_.reduce` except that it iterates over elements of
     * `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {*} [accumulator] The initial value.
     * @returns {*} Returns the accumulated value.
     * @see _.reduce
     * @example
     *
     * var array = [[0, 1], [2, 3], [4, 5]];
     *
     * _.reduceRight(array, function(flattened, other) {
     *   return flattened.concat(other);
     * }, []);
     * // => [4, 5, 2, 3, 0, 1]
     */
    function reduceRight(collection, iteratee, accumulator) {
      var func = isArray(collection) ? arrayReduceRight : baseReduce,
          initAccum = arguments.length < 3;

      return func(collection, getIteratee(iteratee, 4), accumulator, initAccum, baseEachRight);
    }

    /**
     * The opposite of `_.filter`; this method returns the elements of `collection`
     * that `predicate` does **not** return truthy for.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {Array} Returns the new filtered array.
     * @see _.filter
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': false },
     *   { 'user': 'fred',   'age': 40, 'active': true }
     * ];
     *
     * _.reject(users, function(o) { return !o.active; });
     * // => objects for ['fred']
     *
     * // The `_.matches` iteratee shorthand.
     * _.reject(users, { 'age': 40, 'active': true });
     * // => objects for ['barney']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.reject(users, ['active', false]);
     * // => objects for ['fred']
     *
     * // The `_.property` iteratee shorthand.
     * _.reject(users, 'active');
     * // => objects for ['barney']
     */
    function reject(collection, predicate) {
      var func = isArray(collection) ? arrayFilter : baseFilter;
      predicate = getIteratee(predicate, 3);
      return func(collection, function(value, index, collection) {
        return !predicate(value, index, collection);
      });
    }

    /**
     * Gets a random element from `collection`.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to sample.
     * @returns {*} Returns the random element.
     * @example
     *
     * _.sample([1, 2, 3, 4]);
     * // => 2
     */
    function sample(collection) {
      var array = isArrayLike(collection) ? collection : values(collection),
          length = array.length;

      return length > 0 ? array[baseRandom(0, length - 1)] : undefined;
    }

    /**
     * Gets `n` random elements at unique keys from `collection` up to the
     * size of `collection`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to sample.
     * @param {number} [n=1] The number of elements to sample.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the random elements.
     * @example
     *
     * _.sampleSize([1, 2, 3], 2);
     * // => [3, 1]
     *
     * _.sampleSize([1, 2, 3], 4);
     * // => [2, 3, 1]
     */
    function sampleSize(collection, n, guard) {
      var index = -1,
          result = toArray(collection),
          length = result.length,
          lastIndex = length - 1;

      if ((guard ? isIterateeCall(collection, n, guard) : n === undefined)) {
        n = 1;
      } else {
        n = baseClamp(toInteger(n), 0, length);
      }
      while (++index < n) {
        var rand = baseRandom(index, lastIndex),
            value = result[rand];

        result[rand] = result[index];
        result[index] = value;
      }
      result.length = n;
      return result;
    }

    /**
     * Creates an array of shuffled values, using a version of the
     * [Fisher-Yates shuffle](https://en.wikipedia.org/wiki/Fisher-Yates_shuffle).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to shuffle.
     * @returns {Array} Returns the new shuffled array.
     * @example
     *
     * _.shuffle([1, 2, 3, 4]);
     * // => [4, 1, 3, 2]
     */
    function shuffle(collection) {
      return sampleSize(collection, MAX_ARRAY_LENGTH);
    }

    /**
     * Gets the size of `collection` by returning its length for array-like
     * values or the number of own enumerable string keyed properties for objects.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to inspect.
     * @returns {number} Returns the collection size.
     * @example
     *
     * _.size([1, 2, 3]);
     * // => 3
     *
     * _.size({ 'a': 1, 'b': 2 });
     * // => 2
     *
     * _.size('pebbles');
     * // => 7
     */
    function size(collection) {
      if (collection == null) {
        return 0;
      }
      if (isArrayLike(collection)) {
        var result = collection.length;
        return (result && isString(collection)) ? stringSize(collection) : result;
      }
      if (isObjectLike(collection)) {
        var tag = getTag(collection);
        if (tag == mapTag || tag == setTag) {
          return collection.size;
        }
      }
      return keys(collection).length;
    }

    /**
     * Checks if `predicate` returns truthy for **any** element of `collection`.
     * Iteration is stopped once `predicate` returns truthy. The predicate is
     * invoked with three arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {boolean} Returns `true` if any element passes the predicate check,
     *  else `false`.
     * @example
     *
     * _.some([null, 0, 'yes', false], Boolean);
     * // => true
     *
     * var users = [
     *   { 'user': 'barney', 'active': true },
     *   { 'user': 'fred',   'active': false }
     * ];
     *
     * // The `_.matches` iteratee shorthand.
     * _.some(users, { 'user': 'barney', 'active': false });
     * // => false
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.some(users, ['active', false]);
     * // => true
     *
     * // The `_.property` iteratee shorthand.
     * _.some(users, 'active');
     * // => true
     */
    function some(collection, predicate, guard) {
      var func = isArray(collection) ? arraySome : baseSome;
      if (guard && isIterateeCall(collection, predicate, guard)) {
        predicate = undefined;
      }
      return func(collection, getIteratee(predicate, 3));
    }

    /**
     * Creates an array of elements, sorted in ascending order by the results of
     * running each element in a collection thru each iteratee. This method
     * performs a stable sort, that is, it preserves the original sort order of
     * equal elements. The iteratees are invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {...(Array|Array[]|Function|Function[]|Object|Object[]|string|string[])}
     *  [iteratees=[_.identity]] The iteratees to sort by.
     * @returns {Array} Returns the new sorted array.
     * @example
     *
     * var users = [
     *   { 'user': 'fred',   'age': 48 },
     *   { 'user': 'barney', 'age': 36 },
     *   { 'user': 'fred',   'age': 40 },
     *   { 'user': 'barney', 'age': 34 }
     * ];
     *
     * _.sortBy(users, function(o) { return o.user; });
     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 40]]
     *
     * _.sortBy(users, ['user', 'age']);
     * // => objects for [['barney', 34], ['barney', 36], ['fred', 40], ['fred', 48]]
     *
     * _.sortBy(users, 'user', function(o) {
     *   return Math.floor(o.age / 10);
     * });
     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 40]]
     */
    var sortBy = rest(function(collection, iteratees) {
      if (collection == null) {
        return [];
      }
      var length = iteratees.length;
      if (length > 1 && isIterateeCall(collection, iteratees[0], iteratees[1])) {
        iteratees = [];
      } else if (length > 2 && isIterateeCall(iteratees[0], iteratees[1], iteratees[2])) {
        iteratees = [iteratees[0]];
      }
      iteratees = (iteratees.length == 1 && isArray(iteratees[0]))
        ? iteratees[0]
        : baseFlatten(iteratees, 1, isFlattenableIteratee);

      return baseOrderBy(collection, iteratees, []);
    });

    /*------------------------------------------------------------------------*/

    /**
     * Gets the timestamp of the number of milliseconds that have elapsed since
     * the Unix epoch (1 January 1970 00:00:00 UTC).
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @type {Function}
     * @category Date
     * @returns {number} Returns the timestamp.
     * @example
     *
     * _.defer(function(stamp) {
     *   console.log(_.now() - stamp);
     * }, _.now());
     * // => Logs the number of milliseconds it took for the deferred function to be invoked.
     */
    var now = Date.now;

    /*------------------------------------------------------------------------*/

    /**
     * The opposite of `_.before`; this method creates a function that invokes
     * `func` once it's called `n` or more times.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {number} n The number of calls before `func` is invoked.
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new restricted function.
     * @example
     *
     * var saves = ['profile', 'settings'];
     *
     * var done = _.after(saves.length, function() {
     *   console.log('done saving!');
     * });
     *
     * _.forEach(saves, function(type) {
     *   asyncSave({ 'type': type, 'complete': done });
     * });
     * // => Logs 'done saving!' after the two async saves have completed.
     */
    function after(n, func) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      n = toInteger(n);
      return function() {
        if (--n < 1) {
          return func.apply(this, arguments);
        }
      };
    }

    /**
     * Creates a function that invokes `func`, with up to `n` arguments,
     * ignoring any additional arguments.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} func The function to cap arguments for.
     * @param {number} [n=func.length] The arity cap.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the new capped function.
     * @example
     *
     * _.map(['6', '8', '10'], _.ary(parseInt, 1));
     * // => [6, 8, 10]
     */
    function ary(func, n, guard) {
      n = guard ? undefined : n;
      n = (func && n == null) ? func.length : n;
      return createWrapper(func, ARY_FLAG, undefined, undefined, undefined, undefined, n);
    }

    /**
     * Creates a function that invokes `func`, with the `this` binding and arguments
     * of the created function, while it's called less than `n` times. Subsequent
     * calls to the created function return the result of the last `func` invocation.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {number} n The number of calls at which `func` is no longer invoked.
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new restricted function.
     * @example
     *
     * jQuery(element).on('click', _.before(5, addContactToList));
     * // => allows adding up to 4 contacts to the list
     */
    function before(n, func) {
      var result;
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      n = toInteger(n);
      return function() {
        if (--n > 0) {
          result = func.apply(this, arguments);
        }
        if (n <= 1) {
          func = undefined;
        }
        return result;
      };
    }

    /**
     * Creates a function that invokes `func` with the `this` binding of `thisArg`
     * and `partials` prepended to the arguments it receives.
     *
     * The `_.bind.placeholder` value, which defaults to `_` in monolithic builds,
     * may be used as a placeholder for partially applied arguments.
     *
     * **Note:** Unlike native `Function#bind` this method doesn't set the "length"
     * property of bound functions.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to bind.
     * @param {*} thisArg The `this` binding of `func`.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new bound function.
     * @example
     *
     * var greet = function(greeting, punctuation) {
     *   return greeting + ' ' + this.user + punctuation;
     * };
     *
     * var object = { 'user': 'fred' };
     *
     * var bound = _.bind(greet, object, 'hi');
     * bound('!');
     * // => 'hi fred!'
     *
     * // Bound with placeholders.
     * var bound = _.bind(greet, object, _, '!');
     * bound('hi');
     * // => 'hi fred!'
     */
    var bind = rest(function(func, thisArg, partials) {
      var bitmask = BIND_FLAG;
      if (partials.length) {
        var holders = replaceHolders(partials, getHolder(bind));
        bitmask |= PARTIAL_FLAG;
      }
      return createWrapper(func, bitmask, thisArg, partials, holders);
    });

    /**
     * Creates a function that invokes the method at `object[key]` with `partials`
     * prepended to the arguments it receives.
     *
     * This method differs from `_.bind` by allowing bound functions to reference
     * methods that may be redefined or don't yet exist. See
     * [Peter Michaux's article](http://peter.michaux.ca/articles/lazy-function-definition-pattern)
     * for more details.
     *
     * The `_.bindKey.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for partially applied arguments.
     *
     * @static
     * @memberOf _
     * @since 0.10.0
     * @category Function
     * @param {Object} object The object to invoke the method on.
     * @param {string} key The key of the method.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new bound function.
     * @example
     *
     * var object = {
     *   'user': 'fred',
     *   'greet': function(greeting, punctuation) {
     *     return greeting + ' ' + this.user + punctuation;
     *   }
     * };
     *
     * var bound = _.bindKey(object, 'greet', 'hi');
     * bound('!');
     * // => 'hi fred!'
     *
     * object.greet = function(greeting, punctuation) {
     *   return greeting + 'ya ' + this.user + punctuation;
     * };
     *
     * bound('!');
     * // => 'hiya fred!'
     *
     * // Bound with placeholders.
     * var bound = _.bindKey(object, 'greet', _, '!');
     * bound('hi');
     * // => 'hiya fred!'
     */
    var bindKey = rest(function(object, key, partials) {
      var bitmask = BIND_FLAG | BIND_KEY_FLAG;
      if (partials.length) {
        var holders = replaceHolders(partials, getHolder(bindKey));
        bitmask |= PARTIAL_FLAG;
      }
      return createWrapper(key, bitmask, object, partials, holders);
    });

    /**
     * Creates a function that accepts arguments of `func` and either invokes
     * `func` returning its result, if at least `arity` number of arguments have
     * been provided, or returns a function that accepts the remaining `func`
     * arguments, and so on. The arity of `func` may be specified if `func.length`
     * is not sufficient.
     *
     * The `_.curry.placeholder` value, which defaults to `_` in monolithic builds,
     * may be used as a placeholder for provided arguments.
     *
     * **Note:** This method doesn't set the "length" property of curried functions.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Function
     * @param {Function} func The function to curry.
     * @param {number} [arity=func.length] The arity of `func`.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the new curried function.
     * @example
     *
     * var abc = function(a, b, c) {
     *   return [a, b, c];
     * };
     *
     * var curried = _.curry(abc);
     *
     * curried(1)(2)(3);
     * // => [1, 2, 3]
     *
     * curried(1, 2)(3);
     * // => [1, 2, 3]
     *
     * curried(1, 2, 3);
     * // => [1, 2, 3]
     *
     * // Curried with placeholders.
     * curried(1)(_, 3)(2);
     * // => [1, 2, 3]
     */
    function curry(func, arity, guard) {
      arity = guard ? undefined : arity;
      var result = createWrapper(func, CURRY_FLAG, undefined, undefined, undefined, undefined, undefined, arity);
      result.placeholder = curry.placeholder;
      return result;
    }

    /**
     * This method is like `_.curry` except that arguments are applied to `func`
     * in the manner of `_.partialRight` instead of `_.partial`.
     *
     * The `_.curryRight.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for provided arguments.
     *
     * **Note:** This method doesn't set the "length" property of curried functions.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} func The function to curry.
     * @param {number} [arity=func.length] The arity of `func`.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the new curried function.
     * @example
     *
     * var abc = function(a, b, c) {
     *   return [a, b, c];
     * };
     *
     * var curried = _.curryRight(abc);
     *
     * curried(3)(2)(1);
     * // => [1, 2, 3]
     *
     * curried(2, 3)(1);
     * // => [1, 2, 3]
     *
     * curried(1, 2, 3);
     * // => [1, 2, 3]
     *
     * // Curried with placeholders.
     * curried(3)(1, _)(2);
     * // => [1, 2, 3]
     */
    function curryRight(func, arity, guard) {
      arity = guard ? undefined : arity;
      var result = createWrapper(func, CURRY_RIGHT_FLAG, undefined, undefined, undefined, undefined, undefined, arity);
      result.placeholder = curryRight.placeholder;
      return result;
    }

    /**
     * Creates a debounced function that delays invoking `func` until after `wait`
     * milliseconds have elapsed since the last time the debounced function was
     * invoked. The debounced function comes with a `cancel` method to cancel
     * delayed `func` invocations and a `flush` method to immediately invoke them.
     * Provide an options object to indicate whether `func` should be invoked on
     * the leading and/or trailing edge of the `wait` timeout. The `func` is invoked
     * with the last arguments provided to the debounced function. Subsequent calls
     * to the debounced function return the result of the last `func` invocation.
     *
     * **Note:** If `leading` and `trailing` options are `true`, `func` is invoked
     * on the trailing edge of the timeout only if the debounced function is
     * invoked more than once during the `wait` timeout.
     *
     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
     * for details over the differences between `_.debounce` and `_.throttle`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to debounce.
     * @param {number} [wait=0] The number of milliseconds to delay.
     * @param {Object} [options={}] The options object.
     * @param {boolean} [options.leading=false]
     *  Specify invoking on the leading edge of the timeout.
     * @param {number} [options.maxWait]
     *  The maximum time `func` is allowed to be delayed before it's invoked.
     * @param {boolean} [options.trailing=true]
     *  Specify invoking on the trailing edge of the timeout.
     * @returns {Function} Returns the new debounced function.
     * @example
     *
     * // Avoid costly calculations while the window size is in flux.
     * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
     *
     * // Invoke `sendMail` when clicked, debouncing subsequent calls.
     * jQuery(element).on('click', _.debounce(sendMail, 300, {
     *   'leading': true,
     *   'trailing': false
     * }));
     *
     * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
     * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
     * var source = new EventSource('/stream');
     * jQuery(source).on('message', debounced);
     *
     * // Cancel the trailing debounced invocation.
     * jQuery(window).on('popstate', debounced.cancel);
     */
    function debounce(func, wait, options) {
      var lastArgs,
          lastThis,
          maxWait,
          result,
          timerId,
          lastCallTime = 0,
          lastInvokeTime = 0,
          leading = false,
          maxing = false,
          trailing = true;

      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      wait = toNumber(wait) || 0;
      if (isObject(options)) {
        leading = !!options.leading;
        maxing = 'maxWait' in options;
        maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
        trailing = 'trailing' in options ? !!options.trailing : trailing;
      }

      function invokeFunc(time) {
        var args = lastArgs,
            thisArg = lastThis;

        lastArgs = lastThis = undefined;
        lastInvokeTime = time;
        result = func.apply(thisArg, args);
        return result;
      }

      function leadingEdge(time) {
        // Reset any `maxWait` timer.
        lastInvokeTime = time;
        // Start the timer for the trailing edge.
        timerId = setTimeout(timerExpired, wait);
        // Invoke the leading edge.
        return leading ? invokeFunc(time) : result;
      }

      function remainingWait(time) {
        var timeSinceLastCall = time - lastCallTime,
            timeSinceLastInvoke = time - lastInvokeTime,
            result = wait - timeSinceLastCall;

        return maxing ? nativeMin(result, maxWait - timeSinceLastInvoke) : result;
      }

      function shouldInvoke(time) {
        var timeSinceLastCall = time - lastCallTime,
            timeSinceLastInvoke = time - lastInvokeTime;

        // Either this is the first call, activity has stopped and we're at the
        // trailing edge, the system time has gone backwards and we're treating
        // it as the trailing edge, or we've hit the `maxWait` limit.
        return (!lastCallTime || (timeSinceLastCall >= wait) ||
          (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
      }

      function timerExpired() {
        var time = now();
        if (shouldInvoke(time)) {
          return trailingEdge(time);
        }
        // Restart the timer.
        timerId = setTimeout(timerExpired, remainingWait(time));
      }

      function trailingEdge(time) {
        clearTimeout(timerId);
        timerId = undefined;

        // Only invoke if we have `lastArgs` which means `func` has been
        // debounced at least once.
        if (trailing && lastArgs) {
          return invokeFunc(time);
        }
        lastArgs = lastThis = undefined;
        return result;
      }

      function cancel() {
        if (timerId !== undefined) {
          clearTimeout(timerId);
        }
        lastCallTime = lastInvokeTime = 0;
        lastArgs = lastThis = timerId = undefined;
      }

      function flush() {
        return timerId === undefined ? result : trailingEdge(now());
      }

      function debounced() {
        var time = now(),
            isInvoking = shouldInvoke(time);

        lastArgs = arguments;
        lastThis = this;
        lastCallTime = time;

        if (isInvoking) {
          if (timerId === undefined) {
            return leadingEdge(lastCallTime);
          }
          if (maxing) {
            // Handle invocations in a tight loop.
            clearTimeout(timerId);
            timerId = setTimeout(timerExpired, wait);
            return invokeFunc(lastCallTime);
          }
        }
        if (timerId === undefined) {
          timerId = setTimeout(timerExpired, wait);
        }
        return result;
      }
      debounced.cancel = cancel;
      debounced.flush = flush;
      return debounced;
    }

    /**
     * Defers invoking the `func` until the current call stack has cleared. Any
     * additional arguments are provided to `func` when it's invoked.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to defer.
     * @param {...*} [args] The arguments to invoke `func` with.
     * @returns {number} Returns the timer id.
     * @example
     *
     * _.defer(function(text) {
     *   console.log(text);
     * }, 'deferred');
     * // => Logs 'deferred' after one or more milliseconds.
     */
    var defer = rest(function(func, args) {
      return baseDelay(func, 1, args);
    });

    /**
     * Invokes `func` after `wait` milliseconds. Any additional arguments are
     * provided to `func` when it's invoked.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to delay.
     * @param {number} wait The number of milliseconds to delay invocation.
     * @param {...*} [args] The arguments to invoke `func` with.
     * @returns {number} Returns the timer id.
     * @example
     *
     * _.delay(function(text) {
     *   console.log(text);
     * }, 1000, 'later');
     * // => Logs 'later' after one second.
     */
    var delay = rest(function(func, wait, args) {
      return baseDelay(func, toNumber(wait) || 0, args);
    });

    /**
     * Creates a function that invokes `func` with arguments reversed.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Function
     * @param {Function} func The function to flip arguments for.
     * @returns {Function} Returns the new flipped function.
     * @example
     *
     * var flipped = _.flip(function() {
     *   return _.toArray(arguments);
     * });
     *
     * flipped('a', 'b', 'c', 'd');
     * // => ['d', 'c', 'b', 'a']
     */
    function flip(func) {
      return createWrapper(func, FLIP_FLAG);
    }

    /**
     * Creates a function that memoizes the result of `func`. If `resolver` is
     * provided, it determines the cache key for storing the result based on the
     * arguments provided to the memoized function. By default, the first argument
     * provided to the memoized function is used as the map cache key. The `func`
     * is invoked with the `this` binding of the memoized function.
     *
     * **Note:** The cache is exposed as the `cache` property on the memoized
     * function. Its creation may be customized by replacing the `_.memoize.Cache`
     * constructor with one whose instances implement the
     * [`Map`](http://ecma-international.org/ecma-262/6.0/#sec-properties-of-the-map-prototype-object)
     * method interface of `delete`, `get`, `has`, and `set`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to have its output memoized.
     * @param {Function} [resolver] The function to resolve the cache key.
     * @returns {Function} Returns the new memoized function.
     * @example
     *
     * var object = { 'a': 1, 'b': 2 };
     * var other = { 'c': 3, 'd': 4 };
     *
     * var values = _.memoize(_.values);
     * values(object);
     * // => [1, 2]
     *
     * values(other);
     * // => [3, 4]
     *
     * object.a = 2;
     * values(object);
     * // => [1, 2]
     *
     * // Modify the result cache.
     * values.cache.set(object, ['a', 'b']);
     * values(object);
     * // => ['a', 'b']
     *
     * // Replace `_.memoize.Cache`.
     * _.memoize.Cache = WeakMap;
     */
    function memoize(func, resolver) {
      if (typeof func != 'function' || (resolver && typeof resolver != 'function')) {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      var memoized = function() {
        var args = arguments,
            key = resolver ? resolver.apply(this, args) : args[0],
            cache = memoized.cache;

        if (cache.has(key)) {
          return cache.get(key);
        }
        var result = func.apply(this, args);
        memoized.cache = cache.set(key, result);
        return result;
      };
      memoized.cache = new (memoize.Cache || MapCache);
      return memoized;
    }

    // Assign cache to `_.memoize`.
    memoize.Cache = MapCache;

    /**
     * Creates a function that negates the result of the predicate `func`. The
     * `func` predicate is invoked with the `this` binding and arguments of the
     * created function.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} predicate The predicate to negate.
     * @returns {Function} Returns the new negated function.
     * @example
     *
     * function isEven(n) {
     *   return n % 2 == 0;
     * }
     *
     * _.filter([1, 2, 3, 4, 5, 6], _.negate(isEven));
     * // => [1, 3, 5]
     */
    function negate(predicate) {
      if (typeof predicate != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      return function() {
        return !predicate.apply(this, arguments);
      };
    }

    /**
     * Creates a function that is restricted to invoking `func` once. Repeat calls
     * to the function return the value of the first invocation. The `func` is
     * invoked with the `this` binding and arguments of the created function.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new restricted function.
     * @example
     *
     * var initialize = _.once(createApplication);
     * initialize();
     * initialize();
     * // `initialize` invokes `createApplication` once
     */
    function once(func) {
      return before(2, func);
    }

    /**
     * Creates a function that invokes `func` with arguments transformed by
     * corresponding `transforms`.
     *
     * @static
     * @since 4.0.0
     * @memberOf _
     * @category Function
     * @param {Function} func The function to wrap.
     * @param {...(Array|Array[]|Function|Function[]|Object|Object[]|string|string[])}
     *  [transforms[_.identity]] The functions to transform.
     * @returns {Function} Returns the new function.
     * @example
     *
     * function doubled(n) {
     *   return n * 2;
     * }
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var func = _.overArgs(function(x, y) {
     *   return [x, y];
     * }, square, doubled);
     *
     * func(9, 3);
     * // => [81, 6]
     *
     * func(10, 5);
     * // => [100, 10]
     */
    var overArgs = rest(function(func, transforms) {
      transforms = (transforms.length == 1 && isArray(transforms[0]))
        ? arrayMap(transforms[0], baseUnary(getIteratee()))
        : arrayMap(baseFlatten(transforms, 1, isFlattenableIteratee), baseUnary(getIteratee()));

      var funcsLength = transforms.length;
      return rest(function(args) {
        var index = -1,
            length = nativeMin(args.length, funcsLength);

        while (++index < length) {
          args[index] = transforms[index].call(this, args[index]);
        }
        return apply(func, this, args);
      });
    });

    /**
     * Creates a function that invokes `func` with `partials` prepended to the
     * arguments it receives. This method is like `_.bind` except it does **not**
     * alter the `this` binding.
     *
     * The `_.partial.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for partially applied arguments.
     *
     * **Note:** This method doesn't set the "length" property of partially
     * applied functions.
     *
     * @static
     * @memberOf _
     * @since 0.2.0
     * @category Function
     * @param {Function} func The function to partially apply arguments to.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new partially applied function.
     * @example
     *
     * var greet = function(greeting, name) {
     *   return greeting + ' ' + name;
     * };
     *
     * var sayHelloTo = _.partial(greet, 'hello');
     * sayHelloTo('fred');
     * // => 'hello fred'
     *
     * // Partially applied with placeholders.
     * var greetFred = _.partial(greet, _, 'fred');
     * greetFred('hi');
     * // => 'hi fred'
     */
    var partial = rest(function(func, partials) {
      var holders = replaceHolders(partials, getHolder(partial));
      return createWrapper(func, PARTIAL_FLAG, undefined, partials, holders);
    });

    /**
     * This method is like `_.partial` except that partially applied arguments
     * are appended to the arguments it receives.
     *
     * The `_.partialRight.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for partially applied arguments.
     *
     * **Note:** This method doesn't set the "length" property of partially
     * applied functions.
     *
     * @static
     * @memberOf _
     * @since 1.0.0
     * @category Function
     * @param {Function} func The function to partially apply arguments to.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new partially applied function.
     * @example
     *
     * var greet = function(greeting, name) {
     *   return greeting + ' ' + name;
     * };
     *
     * var greetFred = _.partialRight(greet, 'fred');
     * greetFred('hi');
     * // => 'hi fred'
     *
     * // Partially applied with placeholders.
     * var sayHelloTo = _.partialRight(greet, 'hello', _);
     * sayHelloTo('fred');
     * // => 'hello fred'
     */
    var partialRight = rest(function(func, partials) {
      var holders = replaceHolders(partials, getHolder(partialRight));
      return createWrapper(func, PARTIAL_RIGHT_FLAG, undefined, partials, holders);
    });

    /**
     * Creates a function that invokes `func` with arguments arranged according
     * to the specified `indexes` where the argument value at the first index is
     * provided as the first argument, the argument value at the second index is
     * provided as the second argument, and so on.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} func The function to rearrange arguments for.
     * @param {...(number|number[])} indexes The arranged argument indexes.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var rearged = _.rearg(function(a, b, c) {
     *   return [a, b, c];
     * }, 2, 0, 1);
     *
     * rearged('b', 'c', 'a')
     * // => ['a', 'b', 'c']
     */
    var rearg = rest(function(func, indexes) {
      return createWrapper(func, REARG_FLAG, undefined, undefined, undefined, baseFlatten(indexes, 1));
    });

    /**
     * Creates a function that invokes `func` with the `this` binding of the
     * created function and arguments from `start` and beyond provided as
     * an array.
     *
     * **Note:** This method is based on the
     * [rest parameter](https://mdn.io/rest_parameters).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Function
     * @param {Function} func The function to apply a rest parameter to.
     * @param {number} [start=func.length-1] The start position of the rest parameter.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var say = _.rest(function(what, names) {
     *   return what + ' ' + _.initial(names).join(', ') +
     *     (_.size(names) > 1 ? ', & ' : '') + _.last(names);
     * });
     *
     * say('hello', 'fred', 'barney', 'pebbles');
     * // => 'hello fred, barney, & pebbles'
     */
    function rest(func, start) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      start = nativeMax(start === undefined ? (func.length - 1) : toInteger(start), 0);
      return function() {
        var args = arguments,
            index = -1,
            length = nativeMax(args.length - start, 0),
            array = Array(length);

        while (++index < length) {
          array[index] = args[start + index];
        }
        switch (start) {
          case 0: return func.call(this, array);
          case 1: return func.call(this, args[0], array);
          case 2: return func.call(this, args[0], args[1], array);
        }
        var otherArgs = Array(start + 1);
        index = -1;
        while (++index < start) {
          otherArgs[index] = args[index];
        }
        otherArgs[start] = array;
        return apply(func, this, otherArgs);
      };
    }

    /**
     * Creates a function that invokes `func` with the `this` binding of the
     * create function and an array of arguments much like
     * [`Function#apply`](http://www.ecma-international.org/ecma-262/6.0/#sec-function.prototype.apply).
     *
     * **Note:** This method is based on the
     * [spread operator](https://mdn.io/spread_operator).
     *
     * @static
     * @memberOf _
     * @since 3.2.0
     * @category Function
     * @param {Function} func The function to spread arguments over.
     * @param {number} [start=0] The start position of the spread.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var say = _.spread(function(who, what) {
     *   return who + ' says ' + what;
     * });
     *
     * say(['fred', 'hello']);
     * // => 'fred says hello'
     *
     * var numbers = Promise.all([
     *   Promise.resolve(40),
     *   Promise.resolve(36)
     * ]);
     *
     * numbers.then(_.spread(function(x, y) {
     *   return x + y;
     * }));
     * // => a Promise of 76
     */
    function spread(func, start) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      start = start === undefined ? 0 : nativeMax(toInteger(start), 0);
      return rest(function(args) {
        var array = args[start],
            otherArgs = castSlice(args, 0, start);

        if (array) {
          arrayPush(otherArgs, array);
        }
        return apply(func, this, otherArgs);
      });
    }

    /**
     * Creates a throttled function that only invokes `func` at most once per
     * every `wait` milliseconds. The throttled function comes with a `cancel`
     * method to cancel delayed `func` invocations and a `flush` method to
     * immediately invoke them. Provide an options object to indicate whether
     * `func` should be invoked on the leading and/or trailing edge of the `wait`
     * timeout. The `func` is invoked with the last arguments provided to the
     * throttled function. Subsequent calls to the throttled function return the
     * result of the last `func` invocation.
     *
     * **Note:** If `leading` and `trailing` options are `true`, `func` is
     * invoked on the trailing edge of the timeout only if the throttled function
     * is invoked more than once during the `wait` timeout.
     *
     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
     * for details over the differences between `_.throttle` and `_.debounce`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to throttle.
     * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
     * @param {Object} [options={}] The options object.
     * @param {boolean} [options.leading=true]
     *  Specify invoking on the leading edge of the timeout.
     * @param {boolean} [options.trailing=true]
     *  Specify invoking on the trailing edge of the timeout.
     * @returns {Function} Returns the new throttled function.
     * @example
     *
     * // Avoid excessively updating the position while scrolling.
     * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
     *
     * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
     * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
     * jQuery(element).on('click', throttled);
     *
     * // Cancel the trailing throttled invocation.
     * jQuery(window).on('popstate', throttled.cancel);
     */
    function throttle(func, wait, options) {
      var leading = true,
          trailing = true;

      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      if (isObject(options)) {
        leading = 'leading' in options ? !!options.leading : leading;
        trailing = 'trailing' in options ? !!options.trailing : trailing;
      }
      return debounce(func, wait, {
        'leading': leading,
        'maxWait': wait,
        'trailing': trailing
      });
    }

    /**
     * Creates a function that accepts up to one argument, ignoring any
     * additional arguments.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Function
     * @param {Function} func The function to cap arguments for.
     * @returns {Function} Returns the new capped function.
     * @example
     *
     * _.map(['6', '8', '10'], _.unary(parseInt));
     * // => [6, 8, 10]
     */
    function unary(func) {
      return ary(func, 1);
    }

    /**
     * Creates a function that provides `value` to the wrapper function as its
     * first argument. Any additional arguments provided to the function are
     * appended to those provided to the wrapper function. The wrapper is invoked
     * with the `this` binding of the created function.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {*} value The value to wrap.
     * @param {Function} [wrapper=identity] The wrapper function.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var p = _.wrap(_.escape, function(func, text) {
     *   return '<p>' + func(text) + '</p>';
     * });
     *
     * p('fred, barney, & pebbles');
     * // => '<p>fred, barney, &amp; pebbles</p>'
     */
    function wrap(value, wrapper) {
      wrapper = wrapper == null ? identity : wrapper;
      return partial(wrapper, value);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Casts `value` as an array if it's not one.
     *
     * @static
     * @memberOf _
     * @since 4.4.0
     * @category Lang
     * @param {*} value The value to inspect.
     * @returns {Array} Returns the cast array.
     * @example
     *
     * _.castArray(1);
     * // => [1]
     *
     * _.castArray({ 'a': 1 });
     * // => [{ 'a': 1 }]
     *
     * _.castArray('abc');
     * // => ['abc']
     *
     * _.castArray(null);
     * // => [null]
     *
     * _.castArray(undefined);
     * // => [undefined]
     *
     * _.castArray();
     * // => []
     *
     * var array = [1, 2, 3];
     * console.log(_.castArray(array) === array);
     * // => true
     */
    function castArray() {
      if (!arguments.length) {
        return [];
      }
      var value = arguments[0];
      return isArray(value) ? value : [value];
    }

    /**
     * Creates a shallow clone of `value`.
     *
     * **Note:** This method is loosely based on the
     * [structured clone algorithm](https://mdn.io/Structured_clone_algorithm)
     * and supports cloning arrays, array buffers, booleans, date objects, maps,
     * numbers, `Object` objects, regexes, sets, strings, symbols, and typed
     * arrays. The own enumerable properties of `arguments` objects are cloned
     * as plain objects. An empty object is returned for uncloneable values such
     * as error objects, functions, DOM nodes, and WeakMaps.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to clone.
     * @returns {*} Returns the cloned value.
     * @see _.cloneDeep
     * @example
     *
     * var objects = [{ 'a': 1 }, { 'b': 2 }];
     *
     * var shallow = _.clone(objects);
     * console.log(shallow[0] === objects[0]);
     * // => true
     */
    function clone(value) {
      return baseClone(value, false, true);
    }

    /**
     * This method is like `_.clone` except that it accepts `customizer` which
     * is invoked to produce the cloned value. If `customizer` returns `undefined`,
     * cloning is handled by the method instead. The `customizer` is invoked with
     * up to four arguments; (value [, index|key, object, stack]).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to clone.
     * @param {Function} [customizer] The function to customize cloning.
     * @returns {*} Returns the cloned value.
     * @see _.cloneDeepWith
     * @example
     *
     * function customizer(value) {
     *   if (_.isElement(value)) {
     *     return value.cloneNode(false);
     *   }
     * }
     *
     * var el = _.cloneWith(document.body, customizer);
     *
     * console.log(el === document.body);
     * // => false
     * console.log(el.nodeName);
     * // => 'BODY'
     * console.log(el.childNodes.length);
     * // => 0
     */
    function cloneWith(value, customizer) {
      return baseClone(value, false, true, customizer);
    }

    /**
     * This method is like `_.clone` except that it recursively clones `value`.
     *
     * @static
     * @memberOf _
     * @since 1.0.0
     * @category Lang
     * @param {*} value The value to recursively clone.
     * @returns {*} Returns the deep cloned value.
     * @see _.clone
     * @example
     *
     * var objects = [{ 'a': 1 }, { 'b': 2 }];
     *
     * var deep = _.cloneDeep(objects);
     * console.log(deep[0] === objects[0]);
     * // => false
     */
    function cloneDeep(value) {
      return baseClone(value, true, true);
    }

    /**
     * This method is like `_.cloneWith` except that it recursively clones `value`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to recursively clone.
     * @param {Function} [customizer] The function to customize cloning.
     * @returns {*} Returns the deep cloned value.
     * @see _.cloneWith
     * @example
     *
     * function customizer(value) {
     *   if (_.isElement(value)) {
     *     return value.cloneNode(true);
     *   }
     * }
     *
     * var el = _.cloneDeepWith(document.body, customizer);
     *
     * console.log(el === document.body);
     * // => false
     * console.log(el.nodeName);
     * // => 'BODY'
     * console.log(el.childNodes.length);
     * // => 20
     */
    function cloneDeepWith(value, customizer) {
      return baseClone(value, true, true, customizer);
    }

    /**
     * Performs a
     * [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
     * comparison between two values to determine if they are equivalent.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     * @example
     *
     * var object = { 'user': 'fred' };
     * var other = { 'user': 'fred' };
     *
     * _.eq(object, object);
     * // => true
     *
     * _.eq(object, other);
     * // => false
     *
     * _.eq('a', 'a');
     * // => true
     *
     * _.eq('a', Object('a'));
     * // => false
     *
     * _.eq(NaN, NaN);
     * // => true
     */
    function eq(value, other) {
      return value === other || (value !== value && other !== other);
    }

    /**
     * Checks if `value` is greater than `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is greater than `other`,
     *  else `false`.
     * @see _.lt
     * @example
     *
     * _.gt(3, 1);
     * // => true
     *
     * _.gt(3, 3);
     * // => false
     *
     * _.gt(1, 3);
     * // => false
     */
    var gt = createRelationalOperation(baseGt);

    /**
     * Checks if `value` is greater than or equal to `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is greater than or equal to
     *  `other`, else `false`.
     * @see _.lte
     * @example
     *
     * _.gte(3, 1);
     * // => true
     *
     * _.gte(3, 3);
     * // => true
     *
     * _.gte(1, 3);
     * // => false
     */
    var gte = createRelationalOperation(function(value, other) {
      return value >= other;
    });

    /**
     * Checks if `value` is likely an `arguments` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isArguments(function() { return arguments; }());
     * // => true
     *
     * _.isArguments([1, 2, 3]);
     * // => false
     */
    function isArguments(value) {
      // Safari 8.1 incorrectly makes `arguments.callee` enumerable in strict mode.
      return isArrayLikeObject(value) && hasOwnProperty.call(value, 'callee') &&
        (!propertyIsEnumerable.call(value, 'callee') || objectToString.call(value) == argsTag);
    }

    /**
     * Checks if `value` is classified as an `Array` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @type {Function}
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isArray([1, 2, 3]);
     * // => true
     *
     * _.isArray(document.body.children);
     * // => false
     *
     * _.isArray('abc');
     * // => false
     *
     * _.isArray(_.noop);
     * // => false
     */
    var isArray = Array.isArray;

    /**
     * Checks if `value` is classified as an `ArrayBuffer` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isArrayBuffer(new ArrayBuffer(2));
     * // => true
     *
     * _.isArrayBuffer(new Array(2));
     * // => false
     */
    function isArrayBuffer(value) {
      return isObjectLike(value) && objectToString.call(value) == arrayBufferTag;
    }

    /**
     * Checks if `value` is array-like. A value is considered array-like if it's
     * not a function and has a `value.length` that's an integer greater than or
     * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
     * @example
     *
     * _.isArrayLike([1, 2, 3]);
     * // => true
     *
     * _.isArrayLike(document.body.children);
     * // => true
     *
     * _.isArrayLike('abc');
     * // => true
     *
     * _.isArrayLike(_.noop);
     * // => false
     */
    function isArrayLike(value) {
      return value != null && isLength(getLength(value)) && !isFunction(value);
    }

    /**
     * This method is like `_.isArrayLike` except that it also checks if `value`
     * is an object.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an array-like object,
     *  else `false`.
     * @example
     *
     * _.isArrayLikeObject([1, 2, 3]);
     * // => true
     *
     * _.isArrayLikeObject(document.body.children);
     * // => true
     *
     * _.isArrayLikeObject('abc');
     * // => false
     *
     * _.isArrayLikeObject(_.noop);
     * // => false
     */
    function isArrayLikeObject(value) {
      return isObjectLike(value) && isArrayLike(value);
    }

    /**
     * Checks if `value` is classified as a boolean primitive or object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isBoolean(false);
     * // => true
     *
     * _.isBoolean(null);
     * // => false
     */
    function isBoolean(value) {
      return value === true || value === false ||
        (isObjectLike(value) && objectToString.call(value) == boolTag);
    }

    /**
     * Checks if `value` is a buffer.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
     * @example
     *
     * _.isBuffer(new Buffer(2));
     * // => true
     *
     * _.isBuffer(new Uint8Array(2));
     * // => false
     */
    var isBuffer = !Buffer ? constant(false) : function(value) {
      return value instanceof Buffer;
    };

    /**
     * Checks if `value` is classified as a `Date` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isDate(new Date);
     * // => true
     *
     * _.isDate('Mon April 23 2012');
     * // => false
     */
    function isDate(value) {
      return isObjectLike(value) && objectToString.call(value) == dateTag;
    }

    /**
     * Checks if `value` is likely a DOM element.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a DOM element,
     *  else `false`.
     * @example
     *
     * _.isElement(document.body);
     * // => true
     *
     * _.isElement('<body>');
     * // => false
     */
    function isElement(value) {
      return !!value && value.nodeType === 1 && isObjectLike(value) && !isPlainObject(value);
    }

    /**
     * Checks if `value` is an empty object, collection, map, or set.
     *
     * Objects are considered empty if they have no own enumerable string keyed
     * properties.
     *
     * Array-like values such as `arguments` objects, arrays, buffers, strings, or
     * jQuery-like collections are considered empty if they have a `length` of `0`.
     * Similarly, maps and sets are considered empty if they have a `size` of `0`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is empty, else `false`.
     * @example
     *
     * _.isEmpty(null);
     * // => true
     *
     * _.isEmpty(true);
     * // => true
     *
     * _.isEmpty(1);
     * // => true
     *
     * _.isEmpty([1, 2, 3]);
     * // => false
     *
     * _.isEmpty({ 'a': 1 });
     * // => false
     */
    function isEmpty(value) {
      if (isArrayLike(value) &&
          (isArray(value) || isString(value) || isFunction(value.splice) ||
            isArguments(value) || isBuffer(value))) {
        return !value.length;
      }
      if (isObjectLike(value)) {
        var tag = getTag(value);
        if (tag == mapTag || tag == setTag) {
          return !value.size;
        }
      }
      for (var key in value) {
        if (hasOwnProperty.call(value, key)) {
          return false;
        }
      }
      return !(nonEnumShadows && keys(value).length);
    }

    /**
     * Performs a deep comparison between two values to determine if they are
     * equivalent.
     *
     * **Note:** This method supports comparing arrays, array buffers, booleans,
     * date objects, error objects, maps, numbers, `Object` objects, regexes,
     * sets, strings, symbols, and typed arrays. `Object` objects are compared
     * by their own, not inherited, enumerable properties. Functions and DOM
     * nodes are **not** supported.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if the values are equivalent,
     *  else `false`.
     * @example
     *
     * var object = { 'user': 'fred' };
     * var other = { 'user': 'fred' };
     *
     * _.isEqual(object, other);
     * // => true
     *
     * object === other;
     * // => false
     */
    function isEqual(value, other) {
      return baseIsEqual(value, other);
    }

    /**
     * This method is like `_.isEqual` except that it accepts `customizer` which
     * is invoked to compare values. If `customizer` returns `undefined`, comparisons
     * are handled by the method instead. The `customizer` is invoked with up to
     * six arguments: (objValue, othValue [, index|key, object, other, stack]).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @param {Function} [customizer] The function to customize comparisons.
     * @returns {boolean} Returns `true` if the values are equivalent,
     *  else `false`.
     * @example
     *
     * function isGreeting(value) {
     *   return /^h(?:i|ello)$/.test(value);
     * }
     *
     * function customizer(objValue, othValue) {
     *   if (isGreeting(objValue) && isGreeting(othValue)) {
     *     return true;
     *   }
     * }
     *
     * var array = ['hello', 'goodbye'];
     * var other = ['hi', 'goodbye'];
     *
     * _.isEqualWith(array, other, customizer);
     * // => true
     */
    function isEqualWith(value, other, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      var result = customizer ? customizer(value, other) : undefined;
      return result === undefined ? baseIsEqual(value, other, customizer) : !!result;
    }

    /**
     * Checks if `value` is an `Error`, `EvalError`, `RangeError`, `ReferenceError`,
     * `SyntaxError`, `TypeError`, or `URIError` object.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an error object,
     *  else `false`.
     * @example
     *
     * _.isError(new Error);
     * // => true
     *
     * _.isError(Error);
     * // => false
     */
    function isError(value) {
      if (!isObjectLike(value)) {
        return false;
      }
      return (objectToString.call(value) == errorTag) ||
        (typeof value.message == 'string' && typeof value.name == 'string');
    }

    /**
     * Checks if `value` is a finite primitive number.
     *
     * **Note:** This method is based on
     * [`Number.isFinite`](https://mdn.io/Number/isFinite).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a finite number,
     *  else `false`.
     * @example
     *
     * _.isFinite(3);
     * // => true
     *
     * _.isFinite(Number.MIN_VALUE);
     * // => true
     *
     * _.isFinite(Infinity);
     * // => false
     *
     * _.isFinite('3');
     * // => false
     */
    function isFinite(value) {
      return typeof value == 'number' && nativeIsFinite(value);
    }

    /**
     * Checks if `value` is classified as a `Function` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isFunction(_);
     * // => true
     *
     * _.isFunction(/abc/);
     * // => false
     */
    function isFunction(value) {
      // The use of `Object#toString` avoids issues with the `typeof` operator
      // in Safari 8 which returns 'object' for typed array and weak map constructors,
      // and PhantomJS 1.9 which returns 'function' for `NodeList` instances.
      var tag = isObject(value) ? objectToString.call(value) : '';
      return tag == funcTag || tag == genTag;
    }

    /**
     * Checks if `value` is an integer.
     *
     * **Note:** This method is based on
     * [`Number.isInteger`](https://mdn.io/Number/isInteger).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an integer, else `false`.
     * @example
     *
     * _.isInteger(3);
     * // => true
     *
     * _.isInteger(Number.MIN_VALUE);
     * // => false
     *
     * _.isInteger(Infinity);
     * // => false
     *
     * _.isInteger('3');
     * // => false
     */
    function isInteger(value) {
      return typeof value == 'number' && value == toInteger(value);
    }

    /**
     * Checks if `value` is a valid array-like length.
     *
     * **Note:** This function is loosely based on
     * [`ToLength`](http://ecma-international.org/ecma-262/6.0/#sec-tolength).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a valid length,
     *  else `false`.
     * @example
     *
     * _.isLength(3);
     * // => true
     *
     * _.isLength(Number.MIN_VALUE);
     * // => false
     *
     * _.isLength(Infinity);
     * // => false
     *
     * _.isLength('3');
     * // => false
     */
    function isLength(value) {
      return typeof value == 'number' &&
        value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
    }

    /**
     * Checks if `value` is the
     * [language type](http://www.ecma-international.org/ecma-262/6.0/#sec-ecmascript-language-types)
     * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an object, else `false`.
     * @example
     *
     * _.isObject({});
     * // => true
     *
     * _.isObject([1, 2, 3]);
     * // => true
     *
     * _.isObject(_.noop);
     * // => true
     *
     * _.isObject(null);
     * // => false
     */
    function isObject(value) {
      var type = typeof value;
      return !!value && (type == 'object' || type == 'function');
    }

    /**
     * Checks if `value` is object-like. A value is object-like if it's not `null`
     * and has a `typeof` result of "object".
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
     * @example
     *
     * _.isObjectLike({});
     * // => true
     *
     * _.isObjectLike([1, 2, 3]);
     * // => true
     *
     * _.isObjectLike(_.noop);
     * // => false
     *
     * _.isObjectLike(null);
     * // => false
     */
    function isObjectLike(value) {
      return !!value && typeof value == 'object';
    }

    /**
     * Checks if `value` is classified as a `Map` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isMap(new Map);
     * // => true
     *
     * _.isMap(new WeakMap);
     * // => false
     */
    function isMap(value) {
      return isObjectLike(value) && getTag(value) == mapTag;
    }

    /**
     * Performs a partial deep comparison between `object` and `source` to
     * determine if `object` contains equivalent property values. This method is
     * equivalent to a `_.matches` function when `source` is partially applied.
     *
     * **Note:** This method supports comparing the same values as `_.isEqual`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property values to match.
     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
     * @example
     *
     * var object = { 'user': 'fred', 'age': 40 };
     *
     * _.isMatch(object, { 'age': 40 });
     * // => true
     *
     * _.isMatch(object, { 'age': 36 });
     * // => false
     */
    function isMatch(object, source) {
      return object === source || baseIsMatch(object, source, getMatchData(source));
    }

    /**
     * This method is like `_.isMatch` except that it accepts `customizer` which
     * is invoked to compare values. If `customizer` returns `undefined`, comparisons
     * are handled by the method instead. The `customizer` is invoked with five
     * arguments: (objValue, srcValue, index|key, object, source).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property values to match.
     * @param {Function} [customizer] The function to customize comparisons.
     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
     * @example
     *
     * function isGreeting(value) {
     *   return /^h(?:i|ello)$/.test(value);
     * }
     *
     * function customizer(objValue, srcValue) {
     *   if (isGreeting(objValue) && isGreeting(srcValue)) {
     *     return true;
     *   }
     * }
     *
     * var object = { 'greeting': 'hello' };
     * var source = { 'greeting': 'hi' };
     *
     * _.isMatchWith(object, source, customizer);
     * // => true
     */
    function isMatchWith(object, source, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return baseIsMatch(object, source, getMatchData(source), customizer);
    }

    /**
     * Checks if `value` is `NaN`.
     *
     * **Note:** This method is based on
     * [`Number.isNaN`](https://mdn.io/Number/isNaN) and is not the same as
     * global [`isNaN`](https://mdn.io/isNaN) which returns `true` for
     * `undefined` and other non-number values.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
     * @example
     *
     * _.isNaN(NaN);
     * // => true
     *
     * _.isNaN(new Number(NaN));
     * // => true
     *
     * isNaN(undefined);
     * // => true
     *
     * _.isNaN(undefined);
     * // => false
     */
    function isNaN(value) {
      // An `NaN` primitive is the only value that is not equal to itself.
      // Perform the `toStringTag` check first to avoid errors with some
      // ActiveX objects in IE.
      return isNumber(value) && value != +value;
    }

    /**
     * Checks if `value` is a native function.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a native function,
     *  else `false`.
     * @example
     *
     * _.isNative(Array.prototype.push);
     * // => true
     *
     * _.isNative(_);
     * // => false
     */
    function isNative(value) {
      if (!isObject(value)) {
        return false;
      }
      var pattern = (isFunction(value) || isHostObject(value)) ? reIsNative : reIsHostCtor;
      return pattern.test(toSource(value));
    }

    /**
     * Checks if `value` is `null`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is `null`, else `false`.
     * @example
     *
     * _.isNull(null);
     * // => true
     *
     * _.isNull(void 0);
     * // => false
     */
    function isNull(value) {
      return value === null;
    }

    /**
     * Checks if `value` is `null` or `undefined`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is nullish, else `false`.
     * @example
     *
     * _.isNil(null);
     * // => true
     *
     * _.isNil(void 0);
     * // => true
     *
     * _.isNil(NaN);
     * // => false
     */
    function isNil(value) {
      return value == null;
    }

    /**
     * Checks if `value` is classified as a `Number` primitive or object.
     *
     * **Note:** To exclude `Infinity`, `-Infinity`, and `NaN`, which are
     * classified as numbers, use the `_.isFinite` method.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isNumber(3);
     * // => true
     *
     * _.isNumber(Number.MIN_VALUE);
     * // => true
     *
     * _.isNumber(Infinity);
     * // => true
     *
     * _.isNumber('3');
     * // => false
     */
    function isNumber(value) {
      return typeof value == 'number' ||
        (isObjectLike(value) && objectToString.call(value) == numberTag);
    }

    /**
     * Checks if `value` is a plain object, that is, an object created by the
     * `Object` constructor or one with a `[[Prototype]]` of `null`.
     *
     * @static
     * @memberOf _
     * @since 0.8.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a plain object,
     *  else `false`.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     * }
     *
     * _.isPlainObject(new Foo);
     * // => false
     *
     * _.isPlainObject([1, 2, 3]);
     * // => false
     *
     * _.isPlainObject({ 'x': 0, 'y': 0 });
     * // => true
     *
     * _.isPlainObject(Object.create(null));
     * // => true
     */
    function isPlainObject(value) {
      if (!isObjectLike(value) ||
          objectToString.call(value) != objectTag || isHostObject(value)) {
        return false;
      }
      var proto = getPrototype(value);
      if (proto === null) {
        return true;
      }
      var Ctor = hasOwnProperty.call(proto, 'constructor') && proto.constructor;
      return (typeof Ctor == 'function' &&
        Ctor instanceof Ctor && funcToString.call(Ctor) == objectCtorString);
    }

    /**
     * Checks if `value` is classified as a `RegExp` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isRegExp(/abc/);
     * // => true
     *
     * _.isRegExp('/abc/');
     * // => false
     */
    function isRegExp(value) {
      return isObject(value) && objectToString.call(value) == regexpTag;
    }

    /**
     * Checks if `value` is a safe integer. An integer is safe if it's an IEEE-754
     * double precision number which isn't the result of a rounded unsafe integer.
     *
     * **Note:** This method is based on
     * [`Number.isSafeInteger`](https://mdn.io/Number/isSafeInteger).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a safe integer,
     *  else `false`.
     * @example
     *
     * _.isSafeInteger(3);
     * // => true
     *
     * _.isSafeInteger(Number.MIN_VALUE);
     * // => false
     *
     * _.isSafeInteger(Infinity);
     * // => false
     *
     * _.isSafeInteger('3');
     * // => false
     */
    function isSafeInteger(value) {
      return isInteger(value) && value >= -MAX_SAFE_INTEGER && value <= MAX_SAFE_INTEGER;
    }

    /**
     * Checks if `value` is classified as a `Set` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isSet(new Set);
     * // => true
     *
     * _.isSet(new WeakSet);
     * // => false
     */
    function isSet(value) {
      return isObjectLike(value) && getTag(value) == setTag;
    }

    /**
     * Checks if `value` is classified as a `String` primitive or object.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isString('abc');
     * // => true
     *
     * _.isString(1);
     * // => false
     */
    function isString(value) {
      return typeof value == 'string' ||
        (!isArray(value) && isObjectLike(value) && objectToString.call(value) == stringTag);
    }

    /**
     * Checks if `value` is classified as a `Symbol` primitive or object.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isSymbol(Symbol.iterator);
     * // => true
     *
     * _.isSymbol('abc');
     * // => false
     */
    function isSymbol(value) {
      return typeof value == 'symbol' ||
        (isObjectLike(value) && objectToString.call(value) == symbolTag);
    }

    /**
     * Checks if `value` is classified as a typed array.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isTypedArray(new Uint8Array);
     * // => true
     *
     * _.isTypedArray([]);
     * // => false
     */
    function isTypedArray(value) {
      return isObjectLike(value) &&
        isLength(value.length) && !!typedArrayTags[objectToString.call(value)];
    }

    /**
     * Checks if `value` is `undefined`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is `undefined`, else `false`.
     * @example
     *
     * _.isUndefined(void 0);
     * // => true
     *
     * _.isUndefined(null);
     * // => false
     */
    function isUndefined(value) {
      return value === undefined;
    }

    /**
     * Checks if `value` is classified as a `WeakMap` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isWeakMap(new WeakMap);
     * // => true
     *
     * _.isWeakMap(new Map);
     * // => false
     */
    function isWeakMap(value) {
      return isObjectLike(value) && getTag(value) == weakMapTag;
    }

    /**
     * Checks if `value` is classified as a `WeakSet` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is correctly classified,
     *  else `false`.
     * @example
     *
     * _.isWeakSet(new WeakSet);
     * // => true
     *
     * _.isWeakSet(new Set);
     * // => false
     */
    function isWeakSet(value) {
      return isObjectLike(value) && objectToString.call(value) == weakSetTag;
    }

    /**
     * Checks if `value` is less than `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is less than `other`,
     *  else `false`.
     * @see _.gt
     * @example
     *
     * _.lt(1, 3);
     * // => true
     *
     * _.lt(3, 3);
     * // => false
     *
     * _.lt(3, 1);
     * // => false
     */
    var lt = createRelationalOperation(baseLt);

    /**
     * Checks if `value` is less than or equal to `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is less than or equal to
     *  `other`, else `false`.
     * @see _.gte
     * @example
     *
     * _.lte(1, 3);
     * // => true
     *
     * _.lte(3, 3);
     * // => true
     *
     * _.lte(3, 1);
     * // => false
     */
    var lte = createRelationalOperation(function(value, other) {
      return value <= other;
    });

    /**
     * Converts `value` to an array.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {Array} Returns the converted array.
     * @example
     *
     * _.toArray({ 'a': 1, 'b': 2 });
     * // => [1, 2]
     *
     * _.toArray('abc');
     * // => ['a', 'b', 'c']
     *
     * _.toArray(1);
     * // => []
     *
     * _.toArray(null);
     * // => []
     */
    function toArray(value) {
      if (!value) {
        return [];
      }
      if (isArrayLike(value)) {
        return isString(value) ? stringToArray(value) : copyArray(value);
      }
      if (iteratorSymbol && value[iteratorSymbol]) {
        return iteratorToArray(value[iteratorSymbol]());
      }
      var tag = getTag(value),
          func = tag == mapTag ? mapToArray : (tag == setTag ? setToArray : values);

      return func(value);
    }

    /**
     * Converts `value` to a finite number.
     *
     * @static
     * @memberOf _
     * @since 4.12.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted number.
     * @example
     *
     * _.toFinite(3.2);
     * // => 3.2
     *
     * _.toFinite(Number.MIN_VALUE);
     * // => 5e-324
     *
     * _.toFinite(Infinity);
     * // => 1.7976931348623157e+308
     *
     * _.toFinite('3.2');
     * // => 3.2
     */
    function toFinite(value) {
      if (!value) {
        return value === 0 ? value : 0;
      }
      value = toNumber(value);
      if (value === INFINITY || value === -INFINITY) {
        var sign = (value < 0 ? -1 : 1);
        return sign * MAX_INTEGER;
      }
      return value === value ? value : 0;
    }

    /**
     * Converts `value` to an integer.
     *
     * **Note:** This function is loosely based on
     * [`ToInteger`](http://www.ecma-international.org/ecma-262/6.0/#sec-tointeger).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.toInteger(3.2);
     * // => 3
     *
     * _.toInteger(Number.MIN_VALUE);
     * // => 0
     *
     * _.toInteger(Infinity);
     * // => 1.7976931348623157e+308
     *
     * _.toInteger('3.2');
     * // => 3
     */
    function toInteger(value) {
      var result = toFinite(value),
          remainder = result % 1;

      return result === result ? (remainder ? result - remainder : result) : 0;
    }

    /**
     * Converts `value` to an integer suitable for use as the length of an
     * array-like object.
     *
     * **Note:** This method is based on
     * [`ToLength`](http://ecma-international.org/ecma-262/6.0/#sec-tolength).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.toLength(3.2);
     * // => 3
     *
     * _.toLength(Number.MIN_VALUE);
     * // => 0
     *
     * _.toLength(Infinity);
     * // => 4294967295
     *
     * _.toLength('3.2');
     * // => 3
     */
    function toLength(value) {
      return value ? baseClamp(toInteger(value), 0, MAX_ARRAY_LENGTH) : 0;
    }

    /**
     * Converts `value` to a number.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to process.
     * @returns {number} Returns the number.
     * @example
     *
     * _.toNumber(3.2);
     * // => 3.2
     *
     * _.toNumber(Number.MIN_VALUE);
     * // => 5e-324
     *
     * _.toNumber(Infinity);
     * // => Infinity
     *
     * _.toNumber('3.2');
     * // => 3.2
     */
    function toNumber(value) {
      if (typeof value == 'number') {
        return value;
      }
      if (isSymbol(value)) {
        return NAN;
      }
      if (isObject(value)) {
        var other = isFunction(value.valueOf) ? value.valueOf() : value;
        value = isObject(other) ? (other + '') : other;
      }
      if (typeof value != 'string') {
        return value === 0 ? value : +value;
      }
      value = value.replace(reTrim, '');
      var isBinary = reIsBinary.test(value);
      return (isBinary || reIsOctal.test(value))
        ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
        : (reIsBadHex.test(value) ? NAN : +value);
    }

    /**
     * Converts `value` to a plain object flattening inherited enumerable string
     * keyed properties of `value` to own properties of the plain object.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {Object} Returns the converted plain object.
     * @example
     *
     * function Foo() {
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.assign({ 'a': 1 }, new Foo);
     * // => { 'a': 1, 'b': 2 }
     *
     * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
     * // => { 'a': 1, 'b': 2, 'c': 3 }
     */
    function toPlainObject(value) {
      return copyObject(value, keysIn(value));
    }

    /**
     * Converts `value` to a safe integer. A safe integer can be compared and
     * represented correctly.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.toSafeInteger(3.2);
     * // => 3
     *
     * _.toSafeInteger(Number.MIN_VALUE);
     * // => 0
     *
     * _.toSafeInteger(Infinity);
     * // => 9007199254740991
     *
     * _.toSafeInteger('3.2');
     * // => 3
     */
    function toSafeInteger(value) {
      return baseClamp(toInteger(value), -MAX_SAFE_INTEGER, MAX_SAFE_INTEGER);
    }

    /**
     * Converts `value` to a string. An empty string is returned for `null`
     * and `undefined` values. The sign of `-0` is preserved.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to process.
     * @returns {string} Returns the string.
     * @example
     *
     * _.toString(null);
     * // => ''
     *
     * _.toString(-0);
     * // => '-0'
     *
     * _.toString([1, 2, 3]);
     * // => '1,2,3'
     */
    function toString(value) {
      return value == null ? '' : baseToString(value);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Assigns own enumerable string keyed properties of source objects to the
     * destination object. Source objects are applied from left to right.
     * Subsequent sources overwrite property assignments of previous sources.
     *
     * **Note:** This method mutates `object` and is loosely based on
     * [`Object.assign`](https://mdn.io/Object/assign).
     *
     * @static
     * @memberOf _
     * @since 0.10.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.assignIn
     * @example
     *
     * function Foo() {
     *   this.c = 3;
     * }
     *
     * function Bar() {
     *   this.e = 5;
     * }
     *
     * Foo.prototype.d = 4;
     * Bar.prototype.f = 6;
     *
     * _.assign({ 'a': 1 }, new Foo, new Bar);
     * // => { 'a': 1, 'c': 3, 'e': 5 }
     */
    var assign = createAssigner(function(object, source) {
      if (nonEnumShadows || isPrototype(source) || isArrayLike(source)) {
        copyObject(source, keys(source), object);
        return;
      }
      for (var key in source) {
        if (hasOwnProperty.call(source, key)) {
          assignValue(object, key, source[key]);
        }
      }
    });

    /**
     * This method is like `_.assign` except that it iterates over own and
     * inherited source properties.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias extend
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.assign
     * @example
     *
     * function Foo() {
     *   this.b = 2;
     * }
     *
     * function Bar() {
     *   this.d = 4;
     * }
     *
     * Foo.prototype.c = 3;
     * Bar.prototype.e = 5;
     *
     * _.assignIn({ 'a': 1 }, new Foo, new Bar);
     * // => { 'a': 1, 'b': 2, 'c': 3, 'd': 4, 'e': 5 }
     */
    var assignIn = createAssigner(function(object, source) {
      if (nonEnumShadows || isPrototype(source) || isArrayLike(source)) {
        copyObject(source, keysIn(source), object);
        return;
      }
      for (var key in source) {
        assignValue(object, key, source[key]);
      }
    });

    /**
     * This method is like `_.assignIn` except that it accepts `customizer`
     * which is invoked to produce the assigned values. If `customizer` returns
     * `undefined`, assignment is handled by the method instead. The `customizer`
     * is invoked with five arguments: (objValue, srcValue, key, object, source).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias extendWith
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} sources The source objects.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @see _.assignWith
     * @example
     *
     * function customizer(objValue, srcValue) {
     *   return _.isUndefined(objValue) ? srcValue : objValue;
     * }
     *
     * var defaults = _.partialRight(_.assignInWith, customizer);
     *
     * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
     * // => { 'a': 1, 'b': 2 }
     */
    var assignInWith = createAssigner(function(object, source, srcIndex, customizer) {
      copyObject(source, keysIn(source), object, customizer);
    });

    /**
     * This method is like `_.assign` except that it accepts `customizer`
     * which is invoked to produce the assigned values. If `customizer` returns
     * `undefined`, assignment is handled by the method instead. The `customizer`
     * is invoked with five arguments: (objValue, srcValue, key, object, source).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} sources The source objects.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @see _.assignInWith
     * @example
     *
     * function customizer(objValue, srcValue) {
     *   return _.isUndefined(objValue) ? srcValue : objValue;
     * }
     *
     * var defaults = _.partialRight(_.assignWith, customizer);
     *
     * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
     * // => { 'a': 1, 'b': 2 }
     */
    var assignWith = createAssigner(function(object, source, srcIndex, customizer) {
      copyObject(source, keys(source), object, customizer);
    });

    /**
     * Creates an array of values corresponding to `paths` of `object`.
     *
     * @static
     * @memberOf _
     * @since 1.0.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {...(string|string[])} [paths] The property paths of elements to pick.
     * @returns {Array} Returns the picked values.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }, 4] };
     *
     * _.at(object, ['a[0].b.c', 'a[1]']);
     * // => [3, 4]
     *
     * _.at(['a', 'b', 'c'], 0, 2);
     * // => ['a', 'c']
     */
    var at = rest(function(object, paths) {
      return baseAt(object, baseFlatten(paths, 1));
    });

    /**
     * Creates an object that inherits from the `prototype` object. If a
     * `properties` object is given, its own enumerable string keyed properties
     * are assigned to the created object.
     *
     * @static
     * @memberOf _
     * @since 2.3.0
     * @category Object
     * @param {Object} prototype The object to inherit from.
     * @param {Object} [properties] The properties to assign to the object.
     * @returns {Object} Returns the new object.
     * @example
     *
     * function Shape() {
     *   this.x = 0;
     *   this.y = 0;
     * }
     *
     * function Circle() {
     *   Shape.call(this);
     * }
     *
     * Circle.prototype = _.create(Shape.prototype, {
     *   'constructor': Circle
     * });
     *
     * var circle = new Circle;
     * circle instanceof Circle;
     * // => true
     *
     * circle instanceof Shape;
     * // => true
     */
    function create(prototype, properties) {
      var result = baseCreate(prototype);
      return properties ? baseAssign(result, properties) : result;
    }

    /**
     * Assigns own and inherited enumerable string keyed properties of source
     * objects to the destination object for all destination properties that
     * resolve to `undefined`. Source objects are applied from left to right.
     * Once a property is set, additional values of the same property are ignored.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.defaultsDeep
     * @example
     *
     * _.defaults({ 'user': 'barney' }, { 'age': 36 }, { 'user': 'fred' });
     * // => { 'user': 'barney', 'age': 36 }
     */
    var defaults = rest(function(args) {
      args.push(undefined, assignInDefaults);
      return apply(assignInWith, undefined, args);
    });

    /**
     * This method is like `_.defaults` except that it recursively assigns
     * default properties.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.defaults
     * @example
     *
     * _.defaultsDeep({ 'user': { 'name': 'barney' } }, { 'user': { 'name': 'fred', 'age': 36 } });
     * // => { 'user': { 'name': 'barney', 'age': 36 } }
     *
     */
    var defaultsDeep = rest(function(args) {
      args.push(undefined, mergeDefaults);
      return apply(mergeWith, undefined, args);
    });

    /**
     * This method is like `_.find` except that it returns the key of the first
     * element `predicate` returns truthy for instead of the element itself.
     *
     * @static
     * @memberOf _
     * @since 1.1.0
     * @category Object
     * @param {Object} object The object to search.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {string|undefined} Returns the key of the matched element,
     *  else `undefined`.
     * @example
     *
     * var users = {
     *   'barney':  { 'age': 36, 'active': true },
     *   'fred':    { 'age': 40, 'active': false },
     *   'pebbles': { 'age': 1,  'active': true }
     * };
     *
     * _.findKey(users, function(o) { return o.age < 40; });
     * // => 'barney' (iteration order is not guaranteed)
     *
     * // The `_.matches` iteratee shorthand.
     * _.findKey(users, { 'age': 1, 'active': true });
     * // => 'pebbles'
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findKey(users, ['active', false]);
     * // => 'fred'
     *
     * // The `_.property` iteratee shorthand.
     * _.findKey(users, 'active');
     * // => 'barney'
     */
    function findKey(object, predicate) {
      return baseFind(object, getIteratee(predicate, 3), baseForOwn, true);
    }

    /**
     * This method is like `_.findKey` except that it iterates over elements of
     * a collection in the opposite order.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Object
     * @param {Object} object The object to search.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per iteration.
     * @returns {string|undefined} Returns the key of the matched element,
     *  else `undefined`.
     * @example
     *
     * var users = {
     *   'barney':  { 'age': 36, 'active': true },
     *   'fred':    { 'age': 40, 'active': false },
     *   'pebbles': { 'age': 1,  'active': true }
     * };
     *
     * _.findLastKey(users, function(o) { return o.age < 40; });
     * // => returns 'pebbles' assuming `_.findKey` returns 'barney'
     *
     * // The `_.matches` iteratee shorthand.
     * _.findLastKey(users, { 'age': 36, 'active': true });
     * // => 'barney'
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findLastKey(users, ['active', false]);
     * // => 'fred'
     *
     * // The `_.property` iteratee shorthand.
     * _.findLastKey(users, 'active');
     * // => 'pebbles'
     */
    function findLastKey(object, predicate) {
      return baseFind(object, getIteratee(predicate, 3), baseForOwnRight, true);
    }

    /**
     * Iterates over own and inherited enumerable string keyed properties of an
     * object and invokes `iteratee` for each property. The iteratee is invoked
     * with three arguments: (value, key, object). Iteratee functions may exit
     * iteration early by explicitly returning `false`.
     *
     * @static
     * @memberOf _
     * @since 0.3.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forInRight
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forIn(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'a', 'b', then 'c' (iteration order is not guaranteed).
     */
    function forIn(object, iteratee) {
      return object == null
        ? object
        : baseFor(object, getIteratee(iteratee, 3), keysIn);
    }

    /**
     * This method is like `_.forIn` except that it iterates over properties of
     * `object` in the opposite order.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forIn
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forInRight(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'c', 'b', then 'a' assuming `_.forIn` logs 'a', 'b', then 'c'.
     */
    function forInRight(object, iteratee) {
      return object == null
        ? object
        : baseForRight(object, getIteratee(iteratee, 3), keysIn);
    }

    /**
     * Iterates over own enumerable string keyed properties of an object and
     * invokes `iteratee` for each property. The iteratee is invoked with three
     * arguments: (value, key, object). Iteratee functions may exit iteration
     * early by explicitly returning `false`.
     *
     * @static
     * @memberOf _
     * @since 0.3.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forOwnRight
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forOwn(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'a' then 'b' (iteration order is not guaranteed).
     */
    function forOwn(object, iteratee) {
      return object && baseForOwn(object, getIteratee(iteratee, 3));
    }

    /**
     * This method is like `_.forOwn` except that it iterates over properties of
     * `object` in the opposite order.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forOwn
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forOwnRight(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'b' then 'a' assuming `_.forOwn` logs 'a' then 'b'.
     */
    function forOwnRight(object, iteratee) {
      return object && baseForOwnRight(object, getIteratee(iteratee, 3));
    }

    /**
     * Creates an array of function property names from own enumerable properties
     * of `object`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to inspect.
     * @returns {Array} Returns the function names.
     * @see _.functionsIn
     * @example
     *
     * function Foo() {
     *   this.a = _.constant('a');
     *   this.b = _.constant('b');
     * }
     *
     * Foo.prototype.c = _.constant('c');
     *
     * _.functions(new Foo);
     * // => ['a', 'b']
     */
    function functions(object) {
      return object == null ? [] : baseFunctions(object, keys(object));
    }

    /**
     * Creates an array of function property names from own and inherited
     * enumerable properties of `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to inspect.
     * @returns {Array} Returns the function names.
     * @see _.functions
     * @example
     *
     * function Foo() {
     *   this.a = _.constant('a');
     *   this.b = _.constant('b');
     * }
     *
     * Foo.prototype.c = _.constant('c');
     *
     * _.functionsIn(new Foo);
     * // => ['a', 'b', 'c']
     */
    function functionsIn(object) {
      return object == null ? [] : baseFunctions(object, keysIn(object));
    }

    /**
     * Gets the value at `path` of `object`. If the resolved value is
     * `undefined`, the `defaultValue` is used in its place.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to get.
     * @param {*} [defaultValue] The value returned for `undefined` resolved values.
     * @returns {*} Returns the resolved value.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
     *
     * _.get(object, 'a[0].b.c');
     * // => 3
     *
     * _.get(object, ['a', '0', 'b', 'c']);
     * // => 3
     *
     * _.get(object, 'a.b.c', 'default');
     * // => 'default'
     */
    function get(object, path, defaultValue) {
      var result = object == null ? undefined : baseGet(object, path);
      return result === undefined ? defaultValue : result;
    }

    /**
     * Checks if `path` is a direct property of `object`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path to check.
     * @returns {boolean} Returns `true` if `path` exists, else `false`.
     * @example
     *
     * var object = { 'a': { 'b': 2 } };
     * var other = _.create({ 'a': _.create({ 'b': 2 }) });
     *
     * _.has(object, 'a');
     * // => true
     *
     * _.has(object, 'a.b');
     * // => true
     *
     * _.has(object, ['a', 'b']);
     * // => true
     *
     * _.has(other, 'a');
     * // => false
     */
    function has(object, path) {
      return object != null && hasPath(object, path, baseHas);
    }

    /**
     * Checks if `path` is a direct or inherited property of `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path to check.
     * @returns {boolean} Returns `true` if `path` exists, else `false`.
     * @example
     *
     * var object = _.create({ 'a': _.create({ 'b': 2 }) });
     *
     * _.hasIn(object, 'a');
     * // => true
     *
     * _.hasIn(object, 'a.b');
     * // => true
     *
     * _.hasIn(object, ['a', 'b']);
     * // => true
     *
     * _.hasIn(object, 'b');
     * // => false
     */
    function hasIn(object, path) {
      return object != null && hasPath(object, path, baseHasIn);
    }

    /**
     * Creates an object composed of the inverted keys and values of `object`.
     * If `object` contains duplicate values, subsequent values overwrite
     * property assignments of previous values.
     *
     * @static
     * @memberOf _
     * @since 0.7.0
     * @category Object
     * @param {Object} object The object to invert.
     * @returns {Object} Returns the new inverted object.
     * @example
     *
     * var object = { 'a': 1, 'b': 2, 'c': 1 };
     *
     * _.invert(object);
     * // => { '1': 'c', '2': 'b' }
     */
    var invert = createInverter(function(result, value, key) {
      result[value] = key;
    }, constant(identity));

    /**
     * This method is like `_.invert` except that the inverted object is generated
     * from the results of running each element of `object` thru `iteratee`. The
     * corresponding inverted value of each inverted key is an array of keys
     * responsible for generating the inverted value. The iteratee is invoked
     * with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.1.0
     * @category Object
     * @param {Object} object The object to invert.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {Object} Returns the new inverted object.
     * @example
     *
     * var object = { 'a': 1, 'b': 2, 'c': 1 };
     *
     * _.invertBy(object);
     * // => { '1': ['a', 'c'], '2': ['b'] }
     *
     * _.invertBy(object, function(value) {
     *   return 'group' + value;
     * });
     * // => { 'group1': ['a', 'c'], 'group2': ['b'] }
     */
    var invertBy = createInverter(function(result, value, key) {
      if (hasOwnProperty.call(result, value)) {
        result[value].push(key);
      } else {
        result[value] = [key];
      }
    }, getIteratee);

    /**
     * Invokes the method at `path` of `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the method to invoke.
     * @param {...*} [args] The arguments to invoke the method with.
     * @returns {*} Returns the result of the invoked method.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': [1, 2, 3, 4] } }] };
     *
     * _.invoke(object, 'a[0].b.c.slice', 1, 3);
     * // => [2, 3]
     */
    var invoke = rest(baseInvoke);

    /**
     * Creates an array of the own enumerable property names of `object`.
     *
     * **Note:** Non-object values are coerced to objects. See the
     * [ES spec](http://ecma-international.org/ecma-262/6.0/#sec-object.keys)
     * for more details.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.keys(new Foo);
     * // => ['a', 'b'] (iteration order is not guaranteed)
     *
     * _.keys('hi');
     * // => ['0', '1']
     */
    function keys(object) {
      var isProto = isPrototype(object);
      if (!(isProto || isArrayLike(object))) {
        return baseKeys(object);
      }
      var indexes = indexKeys(object),
          skipIndexes = !!indexes,
          result = indexes || [],
          length = result.length;

      for (var key in object) {
        if (baseHas(object, key) &&
            !(skipIndexes && (key == 'length' || isIndex(key, length))) &&
            !(isProto && key == 'constructor')) {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * Creates an array of the own and inherited enumerable property names of `object`.
     *
     * **Note:** Non-object values are coerced to objects.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.keysIn(new Foo);
     * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
     */
    function keysIn(object) {
      var index = -1,
          isProto = isPrototype(object),
          props = baseKeysIn(object),
          propsLength = props.length,
          indexes = indexKeys(object),
          skipIndexes = !!indexes,
          result = indexes || [],
          length = result.length;

      while (++index < propsLength) {
        var key = props[index];
        if (!(skipIndexes && (key == 'length' || isIndex(key, length))) &&
            !(key == 'constructor' && (isProto || !hasOwnProperty.call(object, key)))) {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * The opposite of `_.mapValues`; this method creates an object with the
     * same values as `object` and keys generated by running each own enumerable
     * string keyed property of `object` thru `iteratee`. The iteratee is invoked
     * with three arguments: (value, key, object).
     *
     * @static
     * @memberOf _
     * @since 3.8.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The function invoked per iteration.
     * @returns {Object} Returns the new mapped object.
     * @see _.mapValues
     * @example
     *
     * _.mapKeys({ 'a': 1, 'b': 2 }, function(value, key) {
     *   return key + value;
     * });
     * // => { 'a1': 1, 'b2': 2 }
     */
    function mapKeys(object, iteratee) {
      var result = {};
      iteratee = getIteratee(iteratee, 3);

      baseForOwn(object, function(value, key, object) {
        result[iteratee(value, key, object)] = value;
      });
      return result;
    }

    /**
     * Creates an object with the same keys as `object` and values generated
     * by running each own enumerable string keyed property of `object` thru
     * `iteratee`. The iteratee is invoked with three arguments:
     * (value, key, object).
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The function invoked per iteration.
     * @returns {Object} Returns the new mapped object.
     * @see _.mapKeys
     * @example
     *
     * var users = {
     *   'fred':    { 'user': 'fred',    'age': 40 },
     *   'pebbles': { 'user': 'pebbles', 'age': 1 }
     * };
     *
     * _.mapValues(users, function(o) { return o.age; });
     * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
     *
     * // The `_.property` iteratee shorthand.
     * _.mapValues(users, 'age');
     * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
     */
    function mapValues(object, iteratee) {
      var result = {};
      iteratee = getIteratee(iteratee, 3);

      baseForOwn(object, function(value, key, object) {
        result[key] = iteratee(value, key, object);
      });
      return result;
    }

    /**
     * This method is like `_.assign` except that it recursively merges own and
     * inherited enumerable string keyed properties of source objects into the
     * destination object. Source properties that resolve to `undefined` are
     * skipped if a destination value exists. Array and plain object properties
     * are merged recursively. Other objects and value types are overridden by
     * assignment. Source objects are applied from left to right. Subsequent
     * sources overwrite property assignments of previous sources.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 0.5.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var users = {
     *   'data': [{ 'user': 'barney' }, { 'user': 'fred' }]
     * };
     *
     * var ages = {
     *   'data': [{ 'age': 36 }, { 'age': 40 }]
     * };
     *
     * _.merge(users, ages);
     * // => { 'data': [{ 'user': 'barney', 'age': 36 }, { 'user': 'fred', 'age': 40 }] }
     */
    var merge = createAssigner(function(object, source, srcIndex) {
      baseMerge(object, source, srcIndex);
    });

    /**
     * This method is like `_.merge` except that it accepts `customizer` which
     * is invoked to produce the merged values of the destination and source
     * properties. If `customizer` returns `undefined`, merging is handled by the
     * method instead. The `customizer` is invoked with seven arguments:
     * (objValue, srcValue, key, object, source, stack).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} sources The source objects.
     * @param {Function} customizer The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @example
     *
     * function customizer(objValue, srcValue) {
     *   if (_.isArray(objValue)) {
     *     return objValue.concat(srcValue);
     *   }
     * }
     *
     * var object = {
     *   'fruits': ['apple'],
     *   'vegetables': ['beet']
     * };
     *
     * var other = {
     *   'fruits': ['banana'],
     *   'vegetables': ['carrot']
     * };
     *
     * _.mergeWith(object, other, customizer);
     * // => { 'fruits': ['apple', 'banana'], 'vegetables': ['beet', 'carrot'] }
     */
    var mergeWith = createAssigner(function(object, source, srcIndex, customizer) {
      baseMerge(object, source, srcIndex, customizer);
    });

    /**
     * The opposite of `_.pick`; this method creates an object composed of the
     * own and inherited enumerable string keyed properties of `object` that are
     * not omitted.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The source object.
     * @param {...(string|string[])} [props] The property identifiers to omit.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.omit(object, ['a', 'c']);
     * // => { 'b': '2' }
     */
    var omit = rest(function(object, props) {
      if (object == null) {
        return {};
      }
      props = arrayMap(baseFlatten(props, 1), toKey);
      return basePick(object, baseDifference(getAllKeysIn(object), props));
    });

    /**
     * The opposite of `_.pickBy`; this method creates an object composed of
     * the own and inherited enumerable string keyed properties of `object` that
     * `predicate` doesn't return truthy for. The predicate is invoked with two
     * arguments: (value, key).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The source object.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per property.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.omitBy(object, _.isNumber);
     * // => { 'b': '2' }
     */
    function omitBy(object, predicate) {
      predicate = getIteratee(predicate);
      return basePickBy(object, function(value, key) {
        return !predicate(value, key);
      });
    }

    /**
     * Creates an object composed of the picked `object` properties.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The source object.
     * @param {...(string|string[])} [props] The property identifiers to pick.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.pick(object, ['a', 'c']);
     * // => { 'a': 1, 'c': 3 }
     */
    var pick = rest(function(object, props) {
      return object == null ? {} : basePick(object, arrayMap(baseFlatten(props, 1), toKey));
    });

    /**
     * Creates an object composed of the `object` properties `predicate` returns
     * truthy for. The predicate is invoked with two arguments: (value, key).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The source object.
     * @param {Array|Function|Object|string} [predicate=_.identity]
     *  The function invoked per property.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.pickBy(object, _.isNumber);
     * // => { 'a': 1, 'c': 3 }
     */
    function pickBy(object, predicate) {
      return object == null ? {} : basePickBy(object, getIteratee(predicate));
    }

    /**
     * This method is like `_.get` except that if the resolved value is a
     * function it's invoked with the `this` binding of its parent object and
     * its result is returned.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to resolve.
     * @param {*} [defaultValue] The value returned for `undefined` resolved values.
     * @returns {*} Returns the resolved value.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c1': 3, 'c2': _.constant(4) } }] };
     *
     * _.result(object, 'a[0].b.c1');
     * // => 3
     *
     * _.result(object, 'a[0].b.c2');
     * // => 4
     *
     * _.result(object, 'a[0].b.c3', 'default');
     * // => 'default'
     *
     * _.result(object, 'a[0].b.c3', _.constant('default'));
     * // => 'default'
     */
    function result(object, path, defaultValue) {
      path = isKey(path, object) ? [path] : castPath(path);

      var index = -1,
          length = path.length;

      // Ensure the loop is entered when path is empty.
      if (!length) {
        object = undefined;
        length = 1;
      }
      while (++index < length) {
        var value = object == null ? undefined : object[toKey(path[index])];
        if (value === undefined) {
          index = length;
          value = defaultValue;
        }
        object = isFunction(value) ? value.call(object) : value;
      }
      return object;
    }

    /**
     * Sets the value at `path` of `object`. If a portion of `path` doesn't exist,
     * it's created. Arrays are created for missing index properties while objects
     * are created for all other missing properties. Use `_.setWith` to customize
     * `path` creation.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
     *
     * _.set(object, 'a[0].b.c', 4);
     * console.log(object.a[0].b.c);
     * // => 4
     *
     * _.set(object, ['x', '0', 'y', 'z'], 5);
     * console.log(object.x[0].y.z);
     * // => 5
     */
    function set(object, path, value) {
      return object == null ? object : baseSet(object, path, value);
    }

    /**
     * This method is like `_.set` except that it accepts `customizer` which is
     * invoked to produce the objects of `path`.  If `customizer` returns `undefined`
     * path creation is handled by the method instead. The `customizer` is invoked
     * with three arguments: (nsValue, key, nsObject).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {*} value The value to set.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = {};
     *
     * _.setWith(object, '[0][1]', 'a', Object);
     * // => { '0': { '1': 'a' } }
     */
    function setWith(object, path, value, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return object == null ? object : baseSet(object, path, value, customizer);
    }

    /**
     * Creates an array of own enumerable string keyed-value pairs for `object`
     * which can be consumed by `_.fromPairs`. If `object` is a map or set, its
     * entries are returned.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias entries
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the key-value pairs.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.toPairs(new Foo);
     * // => [['a', 1], ['b', 2]] (iteration order is not guaranteed)
     */
    var toPairs = createToPairs(keys);

    /**
     * Creates an array of own and inherited enumerable string keyed-value pairs
     * for `object` which can be consumed by `_.fromPairs`. If `object` is a map
     * or set, its entries are returned.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias entriesIn
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the key-value pairs.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.toPairsIn(new Foo);
     * // => [['a', 1], ['b', 2], ['c', 3]] (iteration order is not guaranteed)
     */
    var toPairsIn = createToPairs(keysIn);

    /**
     * An alternative to `_.reduce`; this method transforms `object` to a new
     * `accumulator` object which is the result of running each of its own
     * enumerable string keyed properties thru `iteratee`, with each invocation
     * potentially mutating the `accumulator` object. The iteratee is invoked
     * with four arguments: (accumulator, value, key, object). Iteratee functions
     * may exit iteration early by explicitly returning `false`.
     *
     * @static
     * @memberOf _
     * @since 1.3.0
     * @category Object
     * @param {Array|Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {*} [accumulator] The custom accumulator value.
     * @returns {*} Returns the accumulated value.
     * @example
     *
     * _.transform([2, 3, 4], function(result, n) {
     *   result.push(n *= n);
     *   return n % 2 == 0;
     * }, []);
     * // => [4, 9]
     *
     * _.transform({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
     *   (result[value] || (result[value] = [])).push(key);
     * }, {});
     * // => { '1': ['a', 'c'], '2': ['b'] }
     */
    function transform(object, iteratee, accumulator) {
      var isArr = isArray(object) || isTypedArray(object);
      iteratee = getIteratee(iteratee, 4);

      if (accumulator == null) {
        if (isArr || isObject(object)) {
          var Ctor = object.constructor;
          if (isArr) {
            accumulator = isArray(object) ? new Ctor : [];
          } else {
            accumulator = isFunction(Ctor) ? baseCreate(getPrototype(object)) : {};
          }
        } else {
          accumulator = {};
        }
      }
      (isArr ? arrayEach : baseForOwn)(object, function(value, index, object) {
        return iteratee(accumulator, value, index, object);
      });
      return accumulator;
    }

    /**
     * Removes the property at `path` of `object`.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to unset.
     * @returns {boolean} Returns `true` if the property is deleted, else `false`.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 7 } }] };
     * _.unset(object, 'a[0].b.c');
     * // => true
     *
     * console.log(object);
     * // => { 'a': [{ 'b': {} }] };
     *
     * _.unset(object, ['a', '0', 'b', 'c']);
     * // => true
     *
     * console.log(object);
     * // => { 'a': [{ 'b': {} }] };
     */
    function unset(object, path) {
      return object == null ? true : baseUnset(object, path);
    }

    /**
     * This method is like `_.set` except that accepts `updater` to produce the
     * value to set. Use `_.updateWith` to customize `path` creation. The `updater`
     * is invoked with one argument: (value).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.6.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {Function} updater The function to produce the updated value.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
     *
     * _.update(object, 'a[0].b.c', function(n) { return n * n; });
     * console.log(object.a[0].b.c);
     * // => 9
     *
     * _.update(object, 'x[0].y.z', function(n) { return n ? n + 1 : 0; });
     * console.log(object.x[0].y.z);
     * // => 0
     */
    function update(object, path, updater) {
      return object == null ? object : baseUpdate(object, path, castFunction(updater));
    }

    /**
     * This method is like `_.update` except that it accepts `customizer` which is
     * invoked to produce the objects of `path`.  If `customizer` returns `undefined`
     * path creation is handled by the method instead. The `customizer` is invoked
     * with three arguments: (nsValue, key, nsObject).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.6.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {Function} updater The function to produce the updated value.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = {};
     *
     * _.updateWith(object, '[0][1]', _.constant('a'), Object);
     * // => { '0': { '1': 'a' } }
     */
    function updateWith(object, path, updater, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return object == null ? object : baseUpdate(object, path, castFunction(updater), customizer);
    }

    /**
     * Creates an array of the own enumerable string keyed property values of `object`.
     *
     * **Note:** Non-object values are coerced to objects.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property values.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.values(new Foo);
     * // => [1, 2] (iteration order is not guaranteed)
     *
     * _.values('hi');
     * // => ['h', 'i']
     */
    function values(object) {
      return object ? baseValues(object, keys(object)) : [];
    }

    /**
     * Creates an array of the own and inherited enumerable string keyed property
     * values of `object`.
     *
     * **Note:** Non-object values are coerced to objects.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property values.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.valuesIn(new Foo);
     * // => [1, 2, 3] (iteration order is not guaranteed)
     */
    function valuesIn(object) {
      return object == null ? [] : baseValues(object, keysIn(object));
    }

    /*------------------------------------------------------------------------*/

    /**
     * Clamps `number` within the inclusive `lower` and `upper` bounds.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Number
     * @param {number} number The number to clamp.
     * @param {number} [lower] The lower bound.
     * @param {number} upper The upper bound.
     * @returns {number} Returns the clamped number.
     * @example
     *
     * _.clamp(-10, -5, 5);
     * // => -5
     *
     * _.clamp(10, -5, 5);
     * // => 5
     */
    function clamp(number, lower, upper) {
      if (upper === undefined) {
        upper = lower;
        lower = undefined;
      }
      if (upper !== undefined) {
        upper = toNumber(upper);
        upper = upper === upper ? upper : 0;
      }
      if (lower !== undefined) {
        lower = toNumber(lower);
        lower = lower === lower ? lower : 0;
      }
      return baseClamp(toNumber(number), lower, upper);
    }

    /**
     * Checks if `n` is between `start` and up to, but not including, `end`. If
     * `end` is not specified, it's set to `start` with `start` then set to `0`.
     * If `start` is greater than `end` the params are swapped to support
     * negative ranges.
     *
     * @static
     * @memberOf _
     * @since 3.3.0
     * @category Number
     * @param {number} number The number to check.
     * @param {number} [start=0] The start of the range.
     * @param {number} end The end of the range.
     * @returns {boolean} Returns `true` if `number` is in the range, else `false`.
     * @see _.range, _.rangeRight
     * @example
     *
     * _.inRange(3, 2, 4);
     * // => true
     *
     * _.inRange(4, 8);
     * // => true
     *
     * _.inRange(4, 2);
     * // => false
     *
     * _.inRange(2, 2);
     * // => false
     *
     * _.inRange(1.2, 2);
     * // => true
     *
     * _.inRange(5.2, 4);
     * // => false
     *
     * _.inRange(-3, -2, -6);
     * // => true
     */
    function inRange(number, start, end) {
      start = toNumber(start) || 0;
      if (end === undefined) {
        end = start;
        start = 0;
      } else {
        end = toNumber(end) || 0;
      }
      number = toNumber(number);
      return baseInRange(number, start, end);
    }

    /**
     * Produces a random number between the inclusive `lower` and `upper` bounds.
     * If only one argument is provided a number between `0` and the given number
     * is returned. If `floating` is `true`, or either `lower` or `upper` are
     * floats, a floating-point number is returned instead of an integer.
     *
     * **Note:** JavaScript follows the IEEE-754 standard for resolving
     * floating-point values which can produce unexpected results.
     *
     * @static
     * @memberOf _
     * @since 0.7.0
     * @category Number
     * @param {number} [lower=0] The lower bound.
     * @param {number} [upper=1] The upper bound.
     * @param {boolean} [floating] Specify returning a floating-point number.
     * @returns {number} Returns the random number.
     * @example
     *
     * _.random(0, 5);
     * // => an integer between 0 and 5
     *
     * _.random(5);
     * // => also an integer between 0 and 5
     *
     * _.random(5, true);
     * // => a floating-point number between 0 and 5
     *
     * _.random(1.2, 5.2);
     * // => a floating-point number between 1.2 and 5.2
     */
    function random(lower, upper, floating) {
      if (floating && typeof floating != 'boolean' && isIterateeCall(lower, upper, floating)) {
        upper = floating = undefined;
      }
      if (floating === undefined) {
        if (typeof upper == 'boolean') {
          floating = upper;
          upper = undefined;
        }
        else if (typeof lower == 'boolean') {
          floating = lower;
          lower = undefined;
        }
      }
      if (lower === undefined && upper === undefined) {
        lower = 0;
        upper = 1;
      }
      else {
        lower = toNumber(lower) || 0;
        if (upper === undefined) {
          upper = lower;
          lower = 0;
        } else {
          upper = toNumber(upper) || 0;
        }
      }
      if (lower > upper) {
        var temp = lower;
        lower = upper;
        upper = temp;
      }
      if (floating || lower % 1 || upper % 1) {
        var rand = nativeRandom();
        return nativeMin(lower + (rand * (upper - lower + freeParseFloat('1e-' + ((rand + '').length - 1)))), upper);
      }
      return baseRandom(lower, upper);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Converts `string` to [camel case](https://en.wikipedia.org/wiki/CamelCase).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the camel cased string.
     * @example
     *
     * _.camelCase('Foo Bar');
     * // => 'fooBar'
     *
     * _.camelCase('--foo-bar--');
     * // => 'fooBar'
     *
     * _.camelCase('__FOO_BAR__');
     * // => 'fooBar'
     */
    var camelCase = createCompounder(function(result, word, index) {
      word = word.toLowerCase();
      return result + (index ? capitalize(word) : word);
    });

    /**
     * Converts the first character of `string` to upper case and the remaining
     * to lower case.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to capitalize.
     * @returns {string} Returns the capitalized string.
     * @example
     *
     * _.capitalize('FRED');
     * // => 'Fred'
     */
    function capitalize(string) {
      return upperFirst(toString(string).toLowerCase());
    }

    /**
     * Deburrs `string` by converting
     * [latin-1 supplementary letters](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
     * to basic latin letters and removing
     * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to deburr.
     * @returns {string} Returns the deburred string.
     * @example
     *
     * _.deburr('déjà vu');
     * // => 'deja vu'
     */
    function deburr(string) {
      string = toString(string);
      return string && string.replace(reLatin1, deburrLetter).replace(reComboMark, '');
    }

    /**
     * Checks if `string` ends with the given target string.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to search.
     * @param {string} [target] The string to search for.
     * @param {number} [position=string.length] The position to search from.
     * @returns {boolean} Returns `true` if `string` ends with `target`,
     *  else `false`.
     * @example
     *
     * _.endsWith('abc', 'c');
     * // => true
     *
     * _.endsWith('abc', 'b');
     * // => false
     *
     * _.endsWith('abc', 'b', 2);
     * // => true
     */
    function endsWith(string, target, position) {
      string = toString(string);
      target = baseToString(target);

      var length = string.length;
      position = position === undefined
        ? length
        : baseClamp(toInteger(position), 0, length);

      position -= target.length;
      return position >= 0 && string.indexOf(target, position) == position;
    }

    /**
     * Converts the characters "&", "<", ">", '"', "'", and "\`" in `string` to
     * their corresponding HTML entities.
     *
     * **Note:** No other characters are escaped. To escape additional
     * characters use a third-party library like [_he_](https://mths.be/he).
     *
     * Though the ">" character is escaped for symmetry, characters like
     * ">" and "/" don't need escaping in HTML and have no special meaning
     * unless they're part of a tag or unquoted attribute value. See
     * [Mathias Bynens's article](https://mathiasbynens.be/notes/ambiguous-ampersands)
     * (under "semi-related fun fact") for more details.
     *
     * Backticks are escaped because in IE < 9, they can break out of
     * attribute values or HTML comments. See [#59](https://html5sec.org/#59),
     * [#102](https://html5sec.org/#102), [#108](https://html5sec.org/#108), and
     * [#133](https://html5sec.org/#133) of the
     * [HTML5 Security Cheatsheet](https://html5sec.org/) for more details.
     *
     * When working with HTML you should always
     * [quote attribute values](http://wonko.com/post/html-escaping) to reduce
     * XSS vectors.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category String
     * @param {string} [string=''] The string to escape.
     * @returns {string} Returns the escaped string.
     * @example
     *
     * _.escape('fred, barney, & pebbles');
     * // => 'fred, barney, &amp; pebbles'
     */
    function escape(string) {
      string = toString(string);
      return (string && reHasUnescapedHtml.test(string))
        ? string.replace(reUnescapedHtml, escapeHtmlChar)
        : string;
    }

    /**
     * Escapes the `RegExp` special characters "^", "$", "\", ".", "*", "+",
     * "?", "(", ")", "[", "]", "{", "}", and "|" in `string`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to escape.
     * @returns {string} Returns the escaped string.
     * @example
     *
     * _.escapeRegExp('[lodash](https://lodash.com/)');
     * // => '\[lodash\]\(https://lodash\.com/\)'
     */
    function escapeRegExp(string) {
      string = toString(string);
      return (string && reHasRegExpChar.test(string))
        ? string.replace(reRegExpChar, '\\$&')
        : string;
    }

    /**
     * Converts `string` to
     * [kebab case](https://en.wikipedia.org/wiki/Letter_case#Special_case_styles).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the kebab cased string.
     * @example
     *
     * _.kebabCase('Foo Bar');
     * // => 'foo-bar'
     *
     * _.kebabCase('fooBar');
     * // => 'foo-bar'
     *
     * _.kebabCase('__FOO_BAR__');
     * // => 'foo-bar'
     */
    var kebabCase = createCompounder(function(result, word, index) {
      return result + (index ? '-' : '') + word.toLowerCase();
    });

    /**
     * Converts `string`, as space separated words, to lower case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the lower cased string.
     * @example
     *
     * _.lowerCase('--Foo-Bar--');
     * // => 'foo bar'
     *
     * _.lowerCase('fooBar');
     * // => 'foo bar'
     *
     * _.lowerCase('__FOO_BAR__');
     * // => 'foo bar'
     */
    var lowerCase = createCompounder(function(result, word, index) {
      return result + (index ? ' ' : '') + word.toLowerCase();
    });

    /**
     * Converts the first character of `string` to lower case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the converted string.
     * @example
     *
     * _.lowerFirst('Fred');
     * // => 'fred'
     *
     * _.lowerFirst('FRED');
     * // => 'fRED'
     */
    var lowerFirst = createCaseFirst('toLowerCase');

    /**
     * Pads `string` on the left and right sides if it's shorter than `length`.
     * Padding characters are truncated if they can't be evenly divided by `length`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to pad.
     * @param {number} [length=0] The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padded string.
     * @example
     *
     * _.pad('abc', 8);
     * // => '  abc   '
     *
     * _.pad('abc', 8, '_-');
     * // => '_-abc_-_'
     *
     * _.pad('abc', 3);
     * // => 'abc'
     */
    function pad(string, length, chars) {
      string = toString(string);
      length = toInteger(length);

      var strLength = length ? stringSize(string) : 0;
      if (!length || strLength >= length) {
        return string;
      }
      var mid = (length - strLength) / 2;
      return (
        createPadding(nativeFloor(mid), chars) +
        string +
        createPadding(nativeCeil(mid), chars)
      );
    }

    /**
     * Pads `string` on the right side if it's shorter than `length`. Padding
     * characters are truncated if they exceed `length`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to pad.
     * @param {number} [length=0] The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padded string.
     * @example
     *
     * _.padEnd('abc', 6);
     * // => 'abc   '
     *
     * _.padEnd('abc', 6, '_-');
     * // => 'abc_-_'
     *
     * _.padEnd('abc', 3);
     * // => 'abc'
     */
    function padEnd(string, length, chars) {
      string = toString(string);
      length = toInteger(length);

      var strLength = length ? stringSize(string) : 0;
      return (length && strLength < length)
        ? (string + createPadding(length - strLength, chars))
        : string;
    }

    /**
     * Pads `string` on the left side if it's shorter than `length`. Padding
     * characters are truncated if they exceed `length`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to pad.
     * @param {number} [length=0] The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padded string.
     * @example
     *
     * _.padStart('abc', 6);
     * // => '   abc'
     *
     * _.padStart('abc', 6, '_-');
     * // => '_-_abc'
     *
     * _.padStart('abc', 3);
     * // => 'abc'
     */
    function padStart(string, length, chars) {
      string = toString(string);
      length = toInteger(length);

      var strLength = length ? stringSize(string) : 0;
      return (length && strLength < length)
        ? (createPadding(length - strLength, chars) + string)
        : string;
    }

    /**
     * Converts `string` to an integer of the specified radix. If `radix` is
     * `undefined` or `0`, a `radix` of `10` is used unless `value` is a
     * hexadecimal, in which case a `radix` of `16` is used.
     *
     * **Note:** This method aligns with the
     * [ES5 implementation](https://es5.github.io/#x15.1.2.2) of `parseInt`.
     *
     * @static
     * @memberOf _
     * @since 1.1.0
     * @category String
     * @param {string} string The string to convert.
     * @param {number} [radix=10] The radix to interpret `value` by.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.parseInt('08');
     * // => 8
     *
     * _.map(['6', '08', '10'], _.parseInt);
     * // => [6, 8, 10]
     */
    function parseInt(string, radix, guard) {
      // Chrome fails to trim leading <BOM> whitespace characters.
      // See https://bugs.chromium.org/p/v8/issues/detail?id=3109 for more details.
      if (guard || radix == null) {
        radix = 0;
      } else if (radix) {
        radix = +radix;
      }
      string = toString(string).replace(reTrim, '');
      return nativeParseInt(string, radix || (reHasHexPrefix.test(string) ? 16 : 10));
    }

    /**
     * Repeats the given string `n` times.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to repeat.
     * @param {number} [n=1] The number of times to repeat the string.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the repeated string.
     * @example
     *
     * _.repeat('*', 3);
     * // => '***'
     *
     * _.repeat('abc', 2);
     * // => 'abcabc'
     *
     * _.repeat('abc', 0);
     * // => ''
     */
    function repeat(string, n, guard) {
      if ((guard ? isIterateeCall(string, n, guard) : n === undefined)) {
        n = 1;
      } else {
        n = toInteger(n);
      }
      return baseRepeat(toString(string), n);
    }

    /**
     * Replaces matches for `pattern` in `string` with `replacement`.
     *
     * **Note:** This method is based on
     * [`String#replace`](https://mdn.io/String/replace).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to modify.
     * @param {RegExp|string} pattern The pattern to replace.
     * @param {Function|string} replacement The match replacement.
     * @returns {string} Returns the modified string.
     * @example
     *
     * _.replace('Hi Fred', 'Fred', 'Barney');
     * // => 'Hi Barney'
     */
    function replace() {
      var args = arguments,
          string = toString(args[0]);

      return args.length < 3 ? string : nativeReplace.call(string, args[1], args[2]);
    }

    /**
     * Converts `string` to
     * [snake case](https://en.wikipedia.org/wiki/Snake_case).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the snake cased string.
     * @example
     *
     * _.snakeCase('Foo Bar');
     * // => 'foo_bar'
     *
     * _.snakeCase('fooBar');
     * // => 'foo_bar'
     *
     * _.snakeCase('--FOO-BAR--');
     * // => 'foo_bar'
     */
    var snakeCase = createCompounder(function(result, word, index) {
      return result + (index ? '_' : '') + word.toLowerCase();
    });

    /**
     * Splits `string` by `separator`.
     *
     * **Note:** This method is based on
     * [`String#split`](https://mdn.io/String/split).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to split.
     * @param {RegExp|string} separator The separator pattern to split by.
     * @param {number} [limit] The length to truncate results to.
     * @returns {Array} Returns the string segments.
     * @example
     *
     * _.split('a-b-c', '-', 2);
     * // => ['a', 'b']
     */
    function split(string, separator, limit) {
      if (limit && typeof limit != 'number' && isIterateeCall(string, separator, limit)) {
        separator = limit = undefined;
      }
      limit = limit === undefined ? MAX_ARRAY_LENGTH : limit >>> 0;
      if (!limit) {
        return [];
      }
      string = toString(string);
      if (string && (
            typeof separator == 'string' ||
            (separator != null && !isRegExp(separator))
          )) {
        separator = baseToString(separator);
        if (separator == '' && reHasComplexSymbol.test(string)) {
          return castSlice(stringToArray(string), 0, limit);
        }
      }
      return nativeSplit.call(string, separator, limit);
    }

    /**
     * Converts `string` to
     * [start case](https://en.wikipedia.org/wiki/Letter_case#Stylistic_or_specialised_usage).
     *
     * @static
     * @memberOf _
     * @since 3.1.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the start cased string.
     * @example
     *
     * _.startCase('--foo-bar--');
     * // => 'Foo Bar'
     *
     * _.startCase('fooBar');
     * // => 'Foo Bar'
     *
     * _.startCase('__FOO_BAR__');
     * // => 'FOO BAR'
     */
    var startCase = createCompounder(function(result, word, index) {
      return result + (index ? ' ' : '') + upperFirst(word);
    });

    /**
     * Checks if `string` starts with the given target string.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to search.
     * @param {string} [target] The string to search for.
     * @param {number} [position=0] The position to search from.
     * @returns {boolean} Returns `true` if `string` starts with `target`,
     *  else `false`.
     * @example
     *
     * _.startsWith('abc', 'a');
     * // => true
     *
     * _.startsWith('abc', 'b');
     * // => false
     *
     * _.startsWith('abc', 'b', 1);
     * // => true
     */
    function startsWith(string, target, position) {
      string = toString(string);
      position = baseClamp(toInteger(position), 0, string.length);
      return string.lastIndexOf(baseToString(target), position) == position;
    }

    /**
     * Creates a compiled template function that can interpolate data properties
     * in "interpolate" delimiters, HTML-escape interpolated data properties in
     * "escape" delimiters, and execute JavaScript in "evaluate" delimiters. Data
     * properties may be accessed as free variables in the template. If a setting
     * object is given, it takes precedence over `_.templateSettings` values.
     *
     * **Note:** In the development build `_.template` utilizes
     * [sourceURLs](http://www.html5rocks.com/en/tutorials/developertools/sourcemaps/#toc-sourceurl)
     * for easier debugging.
     *
     * For more information on precompiling templates see
     * [lodash's custom builds documentation](https://lodash.com/custom-builds).
     *
     * For more information on Chrome extension sandboxes see
     * [Chrome's extensions documentation](https://developer.chrome.com/extensions/sandboxingEval).
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category String
     * @param {string} [string=''] The template string.
     * @param {Object} [options={}] The options object.
     * @param {RegExp} [options.escape=_.templateSettings.escape]
     *  The HTML "escape" delimiter.
     * @param {RegExp} [options.evaluate=_.templateSettings.evaluate]
     *  The "evaluate" delimiter.
     * @param {Object} [options.imports=_.templateSettings.imports]
     *  An object to import into the template as free variables.
     * @param {RegExp} [options.interpolate=_.templateSettings.interpolate]
     *  The "interpolate" delimiter.
     * @param {string} [options.sourceURL='lodash.templateSources[n]']
     *  The sourceURL of the compiled template.
     * @param {string} [options.variable='obj']
     *  The data object variable name.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the compiled template function.
     * @example
     *
     * // Use the "interpolate" delimiter to create a compiled template.
     * var compiled = _.template('hello <%= user %>!');
     * compiled({ 'user': 'fred' });
     * // => 'hello fred!'
     *
     * // Use the HTML "escape" delimiter to escape data property values.
     * var compiled = _.template('<b><%- value %></b>');
     * compiled({ 'value': '<script>' });
     * // => '<b>&lt;script&gt;</b>'
     *
     * // Use the "evaluate" delimiter to execute JavaScript and generate HTML.
     * var compiled = _.template('<% _.forEach(users, function(user) { %><li><%- user %></li><% }); %>');
     * compiled({ 'users': ['fred', 'barney'] });
     * // => '<li>fred</li><li>barney</li>'
     *
     * // Use the internal `print` function in "evaluate" delimiters.
     * var compiled = _.template('<% print("hello " + user); %>!');
     * compiled({ 'user': 'barney' });
     * // => 'hello barney!'
     *
     * // Use the ES delimiter as an alternative to the default "interpolate" delimiter.
     * var compiled = _.template('hello ${ user }!');
     * compiled({ 'user': 'pebbles' });
     * // => 'hello pebbles!'
     *
     * // Use backslashes to treat delimiters as plain text.
     * var compiled = _.template('<%= "\\<%- value %\\>" %>');
     * compiled({ 'value': 'ignored' });
     * // => '<%- value %>'
     *
     * // Use the `imports` option to import `jQuery` as `jq`.
     * var text = '<% jq.each(users, function(user) { %><li><%- user %></li><% }); %>';
     * var compiled = _.template(text, { 'imports': { 'jq': jQuery } });
     * compiled({ 'users': ['fred', 'barney'] });
     * // => '<li>fred</li><li>barney</li>'
     *
     * // Use the `sourceURL` option to specify a custom sourceURL for the template.
     * var compiled = _.template('hello <%= user %>!', { 'sourceURL': '/basic/greeting.jst' });
     * compiled(data);
     * // => Find the source of "greeting.jst" under the Sources tab or Resources panel of the web inspector.
     *
     * // Use the `variable` option to ensure a with-statement isn't used in the compiled template.
     * var compiled = _.template('hi <%= data.user %>!', { 'variable': 'data' });
     * compiled.source;
     * // => function(data) {
     * //   var __t, __p = '';
     * //   __p += 'hi ' + ((__t = ( data.user )) == null ? '' : __t) + '!';
     * //   return __p;
     * // }
     *
     * // Use custom template delimiters.
     * _.templateSettings.interpolate = /{{([\s\S]+?)}}/g;
     * var compiled = _.template('hello {{ user }}!');
     * compiled({ 'user': 'mustache' });
     * // => 'hello mustache!'
     *
     * // Use the `source` property to inline compiled templates for meaningful
     * // line numbers in error messages and stack traces.
     * fs.writeFileSync(path.join(process.cwd(), 'jst.js'), '\
     *   var JST = {\
     *     "main": ' + _.template(mainText).source + '\
     *   };\
     * ');
     */
    function template(string, options, guard) {
      // Based on John Resig's `tmpl` implementation
      // (http://ejohn.org/blog/javascript-micro-templating/)
      // and Laura Doktorova's doT.js (https://github.com/olado/doT).
      var settings = lodash.templateSettings;

      if (guard && isIterateeCall(string, options, guard)) {
        options = undefined;
      }
      string = toString(string);
      options = assignInWith({}, options, settings, assignInDefaults);

      var imports = assignInWith({}, options.imports, settings.imports, assignInDefaults),
          importsKeys = keys(imports),
          importsValues = baseValues(imports, importsKeys);

      var isEscaping,
          isEvaluating,
          index = 0,
          interpolate = options.interpolate || reNoMatch,
          source = "__p += '";

      // Compile the regexp to match each delimiter.
      var reDelimiters = RegExp(
        (options.escape || reNoMatch).source + '|' +
        interpolate.source + '|' +
        (interpolate === reInterpolate ? reEsTemplate : reNoMatch).source + '|' +
        (options.evaluate || reNoMatch).source + '|$'
      , 'g');

      // Use a sourceURL for easier debugging.
      var sourceURL = '//# sourceURL=' +
        ('sourceURL' in options
          ? options.sourceURL
          : ('lodash.templateSources[' + (++templateCounter) + ']')
        ) + '\n';

      string.replace(reDelimiters, function(match, escapeValue, interpolateValue, esTemplateValue, evaluateValue, offset) {
        interpolateValue || (interpolateValue = esTemplateValue);

        // Escape characters that can't be included in string literals.
        source += string.slice(index, offset).replace(reUnescapedString, escapeStringChar);

        // Replace delimiters with snippets.
        if (escapeValue) {
          isEscaping = true;
          source += "' +\n__e(" + escapeValue + ") +\n'";
        }
        if (evaluateValue) {
          isEvaluating = true;
          source += "';\n" + evaluateValue + ";\n__p += '";
        }
        if (interpolateValue) {
          source += "' +\n((__t = (" + interpolateValue + ")) == null ? '' : __t) +\n'";
        }
        index = offset + match.length;

        // The JS engine embedded in Adobe products needs `match` returned in
        // order to produce the correct `offset` value.
        return match;
      });

      source += "';\n";

      // If `variable` is not specified wrap a with-statement around the generated
      // code to add the data object to the top of the scope chain.
      var variable = options.variable;
      if (!variable) {
        source = 'with (obj) {\n' + source + '\n}\n';
      }
      // Cleanup code by stripping empty strings.
      source = (isEvaluating ? source.replace(reEmptyStringLeading, '') : source)
        .replace(reEmptyStringMiddle, '$1')
        .replace(reEmptyStringTrailing, '$1;');

      // Frame code as the function body.
      source = 'function(' + (variable || 'obj') + ') {\n' +
        (variable
          ? ''
          : 'obj || (obj = {});\n'
        ) +
        "var __t, __p = ''" +
        (isEscaping
           ? ', __e = _.escape'
           : ''
        ) +
        (isEvaluating
          ? ', __j = Array.prototype.join;\n' +
            "function print() { __p += __j.call(arguments, '') }\n"
          : ';\n'
        ) +
        source +
        'return __p\n}';

      var result = attempt(function() {
        return Function(importsKeys, sourceURL + 'return ' + source)
          .apply(undefined, importsValues);
      });

      // Provide the compiled function's source by its `toString` method or
      // the `source` property as a convenience for inlining compiled templates.
      result.source = source;
      if (isError(result)) {
        throw result;
      }
      return result;
    }

    /**
     * Converts `string`, as a whole, to lower case just like
     * [String#toLowerCase](https://mdn.io/toLowerCase).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the lower cased string.
     * @example
     *
     * _.toLower('--Foo-Bar--');
     * // => '--foo-bar--'
     *
     * _.toLower('fooBar');
     * // => 'foobar'
     *
     * _.toLower('__FOO_BAR__');
     * // => '__foo_bar__'
     */
    function toLower(value) {
      return toString(value).toLowerCase();
    }

    /**
     * Converts `string`, as a whole, to upper case just like
     * [String#toUpperCase](https://mdn.io/toUpperCase).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the upper cased string.
     * @example
     *
     * _.toUpper('--foo-bar--');
     * // => '--FOO-BAR--'
     *
     * _.toUpper('fooBar');
     * // => 'FOOBAR'
     *
     * _.toUpper('__foo_bar__');
     * // => '__FOO_BAR__'
     */
    function toUpper(value) {
      return toString(value).toUpperCase();
    }

    /**
     * Removes leading and trailing whitespace or specified characters from `string`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to trim.
     * @param {string} [chars=whitespace] The characters to trim.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the trimmed string.
     * @example
     *
     * _.trim('  abc  ');
     * // => 'abc'
     *
     * _.trim('-_-abc-_-', '_-');
     * // => 'abc'
     *
     * _.map(['  foo  ', '  bar  '], _.trim);
     * // => ['foo', 'bar']
     */
    function trim(string, chars, guard) {
      string = toString(string);
      if (string && (guard || chars === undefined)) {
        return string.replace(reTrim, '');
      }
      if (!string || !(chars = baseToString(chars))) {
        return string;
      }
      var strSymbols = stringToArray(string),
          chrSymbols = stringToArray(chars),
          start = charsStartIndex(strSymbols, chrSymbols),
          end = charsEndIndex(strSymbols, chrSymbols) + 1;

      return castSlice(strSymbols, start, end).join('');
    }

    /**
     * Removes trailing whitespace or specified characters from `string`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to trim.
     * @param {string} [chars=whitespace] The characters to trim.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the trimmed string.
     * @example
     *
     * _.trimEnd('  abc  ');
     * // => '  abc'
     *
     * _.trimEnd('-_-abc-_-', '_-');
     * // => '-_-abc'
     */
    function trimEnd(string, chars, guard) {
      string = toString(string);
      if (string && (guard || chars === undefined)) {
        return string.replace(reTrimEnd, '');
      }
      if (!string || !(chars = baseToString(chars))) {
        return string;
      }
      var strSymbols = stringToArray(string),
          end = charsEndIndex(strSymbols, stringToArray(chars)) + 1;

      return castSlice(strSymbols, 0, end).join('');
    }

    /**
     * Removes leading whitespace or specified characters from `string`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to trim.
     * @param {string} [chars=whitespace] The characters to trim.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the trimmed string.
     * @example
     *
     * _.trimStart('  abc  ');
     * // => 'abc  '
     *
     * _.trimStart('-_-abc-_-', '_-');
     * // => 'abc-_-'
     */
    function trimStart(string, chars, guard) {
      string = toString(string);
      if (string && (guard || chars === undefined)) {
        return string.replace(reTrimStart, '');
      }
      if (!string || !(chars = baseToString(chars))) {
        return string;
      }
      var strSymbols = stringToArray(string),
          start = charsStartIndex(strSymbols, stringToArray(chars));

      return castSlice(strSymbols, start).join('');
    }

    /**
     * Truncates `string` if it's longer than the given maximum string length.
     * The last characters of the truncated string are replaced with the omission
     * string which defaults to "...".
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to truncate.
     * @param {Object} [options={}] The options object.
     * @param {number} [options.length=30] The maximum string length.
     * @param {string} [options.omission='...'] The string to indicate text is omitted.
     * @param {RegExp|string} [options.separator] The separator pattern to truncate to.
     * @returns {string} Returns the truncated string.
     * @example
     *
     * _.truncate('hi-diddly-ho there, neighborino');
     * // => 'hi-diddly-ho there, neighbo...'
     *
     * _.truncate('hi-diddly-ho there, neighborino', {
     *   'length': 24,
     *   'separator': ' '
     * });
     * // => 'hi-diddly-ho there,...'
     *
     * _.truncate('hi-diddly-ho there, neighborino', {
     *   'length': 24,
     *   'separator': /,? +/
     * });
     * // => 'hi-diddly-ho there...'
     *
     * _.truncate('hi-diddly-ho there, neighborino', {
     *   'omission': ' [...]'
     * });
     * // => 'hi-diddly-ho there, neig [...]'
     */
    function truncate(string, options) {
      var length = DEFAULT_TRUNC_LENGTH,
          omission = DEFAULT_TRUNC_OMISSION;

      if (isObject(options)) {
        var separator = 'separator' in options ? options.separator : separator;
        length = 'length' in options ? toInteger(options.length) : length;
        omission = 'omission' in options ? baseToString(options.omission) : omission;
      }
      string = toString(string);

      var strLength = string.length;
      if (reHasComplexSymbol.test(string)) {
        var strSymbols = stringToArray(string);
        strLength = strSymbols.length;
      }
      if (length >= strLength) {
        return string;
      }
      var end = length - stringSize(omission);
      if (end < 1) {
        return omission;
      }
      var result = strSymbols
        ? castSlice(strSymbols, 0, end).join('')
        : string.slice(0, end);

      if (separator === undefined) {
        return result + omission;
      }
      if (strSymbols) {
        end += (result.length - end);
      }
      if (isRegExp(separator)) {
        if (string.slice(end).search(separator)) {
          var match,
              substring = result;

          if (!separator.global) {
            separator = RegExp(separator.source, toString(reFlags.exec(separator)) + 'g');
          }
          separator.lastIndex = 0;
          while ((match = separator.exec(substring))) {
            var newEnd = match.index;
          }
          result = result.slice(0, newEnd === undefined ? end : newEnd);
        }
      } else if (string.indexOf(baseToString(separator), end) != end) {
        var index = result.lastIndexOf(separator);
        if (index > -1) {
          result = result.slice(0, index);
        }
      }
      return result + omission;
    }

    /**
     * The inverse of `_.escape`; this method converts the HTML entities
     * `&amp;`, `&lt;`, `&gt;`, `&quot;`, `&#39;`, and `&#96;` in `string` to
     * their corresponding characters.
     *
     * **Note:** No other HTML entities are unescaped. To unescape additional
     * HTML entities use a third-party library like [_he_](https://mths.be/he).
     *
     * @static
     * @memberOf _
     * @since 0.6.0
     * @category String
     * @param {string} [string=''] The string to unescape.
     * @returns {string} Returns the unescaped string.
     * @example
     *
     * _.unescape('fred, barney, &amp; pebbles');
     * // => 'fred, barney, & pebbles'
     */
    function unescape(string) {
      string = toString(string);
      return (string && reHasEscapedHtml.test(string))
        ? string.replace(reEscapedHtml, unescapeHtmlChar)
        : string;
    }

    /**
     * Converts `string`, as space separated words, to upper case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the upper cased string.
     * @example
     *
     * _.upperCase('--foo-bar');
     * // => 'FOO BAR'
     *
     * _.upperCase('fooBar');
     * // => 'FOO BAR'
     *
     * _.upperCase('__foo_bar__');
     * // => 'FOO BAR'
     */
    var upperCase = createCompounder(function(result, word, index) {
      return result + (index ? ' ' : '') + word.toUpperCase();
    });

    /**
     * Converts the first character of `string` to upper case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the converted string.
     * @example
     *
     * _.upperFirst('fred');
     * // => 'Fred'
     *
     * _.upperFirst('FRED');
     * // => 'FRED'
     */
    var upperFirst = createCaseFirst('toUpperCase');

    /**
     * Splits `string` into an array of its words.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to inspect.
     * @param {RegExp|string} [pattern] The pattern to match words.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the words of `string`.
     * @example
     *
     * _.words('fred, barney, & pebbles');
     * // => ['fred', 'barney', 'pebbles']
     *
     * _.words('fred, barney, & pebbles', /[^, ]+/g);
     * // => ['fred', 'barney', '&', 'pebbles']
     */
    function words(string, pattern, guard) {
      string = toString(string);
      pattern = guard ? undefined : pattern;

      if (pattern === undefined) {
        pattern = reHasComplexWord.test(string) ? reComplexWord : reBasicWord;
      }
      return string.match(pattern) || [];
    }

    /*------------------------------------------------------------------------*/

    /**
     * Attempts to invoke `func`, returning either the result or the caught error
     * object. Any additional arguments are provided to `func` when it's invoked.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {Function} func The function to attempt.
     * @param {...*} [args] The arguments to invoke `func` with.
     * @returns {*} Returns the `func` result or error object.
     * @example
     *
     * // Avoid throwing errors for invalid selectors.
     * var elements = _.attempt(function(selector) {
     *   return document.querySelectorAll(selector);
     * }, '>_>');
     *
     * if (_.isError(elements)) {
     *   elements = [];
     * }
     */
    var attempt = rest(function(func, args) {
      try {
        return apply(func, undefined, args);
      } catch (e) {
        return isError(e) ? e : new Error(e);
      }
    });

    /**
     * Binds methods of an object to the object itself, overwriting the existing
     * method.
     *
     * **Note:** This method doesn't set the "length" property of bound functions.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {Object} object The object to bind and assign the bound methods to.
     * @param {...(string|string[])} methodNames The object method names to bind.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var view = {
     *   'label': 'docs',
     *   'onClick': function() {
     *     console.log('clicked ' + this.label);
     *   }
     * };
     *
     * _.bindAll(view, 'onClick');
     * jQuery(element).on('click', view.onClick);
     * // => Logs 'clicked docs' when clicked.
     */
    var bindAll = rest(function(object, methodNames) {
      arrayEach(baseFlatten(methodNames, 1), function(key) {
        key = toKey(key);
        object[key] = bind(object[key], object);
      });
      return object;
    });

    /**
     * Creates a function that iterates over `pairs` and invokes the corresponding
     * function of the first predicate to return truthy. The predicate-function
     * pairs are invoked with the `this` binding and arguments of the created
     * function.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {Array} pairs The predicate-function pairs.
     * @returns {Function} Returns the new composite function.
     * @example
     *
     * var func = _.cond([
     *   [_.matches({ 'a': 1 }),           _.constant('matches A')],
     *   [_.conforms({ 'b': _.isNumber }), _.constant('matches B')],
     *   [_.constant(true),                _.constant('no match')]
     * ]);
     *
     * func({ 'a': 1, 'b': 2 });
     * // => 'matches A'
     *
     * func({ 'a': 0, 'b': 1 });
     * // => 'matches B'
     *
     * func({ 'a': '1', 'b': '2' });
     * // => 'no match'
     */
    function cond(pairs) {
      var length = pairs ? pairs.length : 0,
          toIteratee = getIteratee();

      pairs = !length ? [] : arrayMap(pairs, function(pair) {
        if (typeof pair[1] != 'function') {
          throw new TypeError(FUNC_ERROR_TEXT);
        }
        return [toIteratee(pair[0]), pair[1]];
      });

      return rest(function(args) {
        var index = -1;
        while (++index < length) {
          var pair = pairs[index];
          if (apply(pair[0], this, args)) {
            return apply(pair[1], this, args);
          }
        }
      });
    }

    /**
     * Creates a function that invokes the predicate properties of `source` with
     * the corresponding property values of a given object, returning `true` if
     * all predicates return truthy, else `false`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {Object} source The object of property predicates to conform to.
     * @returns {Function} Returns the new spec function.
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36 },
     *   { 'user': 'fred',   'age': 40 }
     * ];
     *
     * _.filter(users, _.conforms({ 'age': _.partial(_.gt, _, 38) }));
     * // => [{ 'user': 'fred', 'age': 40 }]
     */
    function conforms(source) {
      return baseConforms(baseClone(source, true));
    }

    /**
     * Creates a function that returns `value`.
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Util
     * @param {*} value The value to return from the new function.
     * @returns {Function} Returns the new constant function.
     * @example
     *
     * var object = { 'user': 'fred' };
     * var getter = _.constant(object);
     *
     * getter() === object;
     * // => true
     */
    function constant(value) {
      return function() {
        return value;
      };
    }

    /**
     * Creates a function that returns the result of invoking the given functions
     * with the `this` binding of the created function, where each successive
     * invocation is supplied the return value of the previous.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {...(Function|Function[])} [funcs] Functions to invoke.
     * @returns {Function} Returns the new composite function.
     * @see _.flowRight
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var addSquare = _.flow(_.add, square);
     * addSquare(1, 2);
     * // => 9
     */
    var flow = createFlow();

    /**
     * This method is like `_.flow` except that it creates a function that
     * invokes the given functions from right to left.
     *
     * @static
     * @since 3.0.0
     * @memberOf _
     * @category Util
     * @param {...(Function|Function[])} [funcs] Functions to invoke.
     * @returns {Function} Returns the new composite function.
     * @see _.flow
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var addSquare = _.flowRight(square, _.add);
     * addSquare(1, 2);
     * // => 9
     */
    var flowRight = createFlow(true);

    /**
     * This method returns the first argument given to it.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {*} value Any value.
     * @returns {*} Returns `value`.
     * @example
     *
     * var object = { 'user': 'fred' };
     *
     * _.identity(object) === object;
     * // => true
     */
    function identity(value) {
      return value;
    }

    /**
     * Creates a function that invokes `func` with the arguments of the created
     * function. If `func` is a property name, the created function returns the
     * property value for a given element. If `func` is an array or object, the
     * created function returns `true` for elements that contain the equivalent
     * source properties, otherwise it returns `false`.
     *
     * @static
     * @since 4.0.0
     * @memberOf _
     * @category Util
     * @param {*} [func=_.identity] The value to convert to a callback.
     * @returns {Function} Returns the callback.
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': true },
     *   { 'user': 'fred',   'age': 40, 'active': false }
     * ];
     *
     * // The `_.matches` iteratee shorthand.
     * _.filter(users, _.iteratee({ 'user': 'barney', 'active': true }));
     * // => [{ 'user': 'barney', 'age': 36, 'active': true }]
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.filter(users, _.iteratee(['user', 'fred']));
     * // => [{ 'user': 'fred', 'age': 40 }]
     *
     * // The `_.property` iteratee shorthand.
     * _.map(users, _.iteratee('user'));
     * // => ['barney', 'fred']
     *
     * // Create custom iteratee shorthands.
     * _.iteratee = _.wrap(_.iteratee, function(iteratee, func) {
     *   return !_.isRegExp(func) ? iteratee(func) : function(string) {
     *     return func.test(string);
     *   };
     * });
     *
     * _.filter(['abc', 'def'], /ef/);
     * // => ['def']
     */
    function iteratee(func) {
      return baseIteratee(typeof func == 'function' ? func : baseClone(func, true));
    }

    /**
     * Creates a function that performs a partial deep comparison between a given
     * object and `source`, returning `true` if the given object has equivalent
     * property values, else `false`. The created function is equivalent to
     * `_.isMatch` with a `source` partially applied.
     *
     * **Note:** This method supports comparing the same values as `_.isEqual`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {Object} source The object of property values to match.
     * @returns {Function} Returns the new spec function.
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': true },
     *   { 'user': 'fred',   'age': 40, 'active': false }
     * ];
     *
     * _.filter(users, _.matches({ 'age': 40, 'active': false }));
     * // => [{ 'user': 'fred', 'age': 40, 'active': false }]
     */
    function matches(source) {
      return baseMatches(baseClone(source, true));
    }

    /**
     * Creates a function that performs a partial deep comparison between the
     * value at `path` of a given object to `srcValue`, returning `true` if the
     * object value is equivalent, else `false`.
     *
     * **Note:** This method supports comparing the same values as `_.isEqual`.
     *
     * @static
     * @memberOf _
     * @since 3.2.0
     * @category Util
     * @param {Array|string} path The path of the property to get.
     * @param {*} srcValue The value to match.
     * @returns {Function} Returns the new spec function.
     * @example
     *
     * var users = [
     *   { 'user': 'barney' },
     *   { 'user': 'fred' }
     * ];
     *
     * _.find(users, _.matchesProperty('user', 'fred'));
     * // => { 'user': 'fred' }
     */
    function matchesProperty(path, srcValue) {
      return baseMatchesProperty(path, baseClone(srcValue, true));
    }

    /**
     * Creates a function that invokes the method at `path` of a given object.
     * Any additional arguments are provided to the invoked method.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Util
     * @param {Array|string} path The path of the method to invoke.
     * @param {...*} [args] The arguments to invoke the method with.
     * @returns {Function} Returns the new invoker function.
     * @example
     *
     * var objects = [
     *   { 'a': { 'b': _.constant(2) } },
     *   { 'a': { 'b': _.constant(1) } }
     * ];
     *
     * _.map(objects, _.method('a.b'));
     * // => [2, 1]
     *
     * _.map(objects, _.method(['a', 'b']));
     * // => [2, 1]
     */
    var method = rest(function(path, args) {
      return function(object) {
        return baseInvoke(object, path, args);
      };
    });

    /**
     * The opposite of `_.method`; this method creates a function that invokes
     * the method at a given path of `object`. Any additional arguments are
     * provided to the invoked method.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Util
     * @param {Object} object The object to query.
     * @param {...*} [args] The arguments to invoke the method with.
     * @returns {Function} Returns the new invoker function.
     * @example
     *
     * var array = _.times(3, _.constant),
     *     object = { 'a': array, 'b': array, 'c': array };
     *
     * _.map(['a[2]', 'c[0]'], _.methodOf(object));
     * // => [2, 0]
     *
     * _.map([['a', '2'], ['c', '0']], _.methodOf(object));
     * // => [2, 0]
     */
    var methodOf = rest(function(object, args) {
      return function(path) {
        return baseInvoke(object, path, args);
      };
    });

    /**
     * Adds all own enumerable string keyed function properties of a source
     * object to the destination object. If `object` is a function, then methods
     * are added to its prototype as well.
     *
     * **Note:** Use `_.runInContext` to create a pristine `lodash` function to
     * avoid conflicts caused by modifying the original.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {Function|Object} [object=lodash] The destination object.
     * @param {Object} source The object of functions to add.
     * @param {Object} [options={}] The options object.
     * @param {boolean} [options.chain=true] Specify whether mixins are chainable.
     * @returns {Function|Object} Returns `object`.
     * @example
     *
     * function vowels(string) {
     *   return _.filter(string, function(v) {
     *     return /[aeiou]/i.test(v);
     *   });
     * }
     *
     * _.mixin({ 'vowels': vowels });
     * _.vowels('fred');
     * // => ['e']
     *
     * _('fred').vowels().value();
     * // => ['e']
     *
     * _.mixin({ 'vowels': vowels }, { 'chain': false });
     * _('fred').vowels();
     * // => ['e']
     */
    function mixin(object, source, options) {
      var props = keys(source),
          methodNames = baseFunctions(source, props);

      if (options == null &&
          !(isObject(source) && (methodNames.length || !props.length))) {
        options = source;
        source = object;
        object = this;
        methodNames = baseFunctions(source, keys(source));
      }
      var chain = !(isObject(options) && 'chain' in options) || !!options.chain,
          isFunc = isFunction(object);

      arrayEach(methodNames, function(methodName) {
        var func = source[methodName];
        object[methodName] = func;
        if (isFunc) {
          object.prototype[methodName] = function() {
            var chainAll = this.__chain__;
            if (chain || chainAll) {
              var result = object(this.__wrapped__),
                  actions = result.__actions__ = copyArray(this.__actions__);

              actions.push({ 'func': func, 'args': arguments, 'thisArg': object });
              result.__chain__ = chainAll;
              return result;
            }
            return func.apply(object, arrayPush([this.value()], arguments));
          };
        }
      });

      return object;
    }

    /**
     * Reverts the `_` variable to its previous value and returns a reference to
     * the `lodash` function.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @returns {Function} Returns the `lodash` function.
     * @example
     *
     * var lodash = _.noConflict();
     */
    function noConflict() {
      if (root._ === this) {
        root._ = oldDash;
      }
      return this;
    }

    /**
     * A no-operation function that returns `undefined` regardless of the
     * arguments it receives.
     *
     * @static
     * @memberOf _
     * @since 2.3.0
     * @category Util
     * @example
     *
     * var object = { 'user': 'fred' };
     *
     * _.noop(object) === undefined;
     * // => true
     */
    function noop() {
      // No operation performed.
    }

    /**
     * Creates a function that gets the argument at `n` index. If `n` is negative,
     * the nth argument from the end is returned.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {number} [n=0] The index of the argument to return.
     * @returns {Function} Returns the new pass-thru function.
     * @example
     *
     * var func = _.nthArg(1);
     * func('a', 'b', 'c', 'd');
     * // => 'b'
     *
     * var func = _.nthArg(-2);
     * func('a', 'b', 'c', 'd');
     * // => 'c'
     */
    function nthArg(n) {
      n = toInteger(n);
      return rest(function(args) {
        return baseNth(args, n);
      });
    }

    /**
     * Creates a function that invokes `iteratees` with the arguments it receives
     * and returns their results.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {...(Array|Array[]|Function|Function[]|Object|Object[]|string|string[])}
     *  [iteratees=[_.identity]] The iteratees to invoke.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var func = _.over(Math.max, Math.min);
     *
     * func(1, 2, 3, 4);
     * // => [4, 1]
     */
    var over = createOver(arrayMap);

    /**
     * Creates a function that checks if **all** of the `predicates` return
     * truthy when invoked with the arguments it receives.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {...(Array|Array[]|Function|Function[]|Object|Object[]|string|string[])}
     *  [predicates=[_.identity]] The predicates to check.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var func = _.overEvery(Boolean, isFinite);
     *
     * func('1');
     * // => true
     *
     * func(null);
     * // => false
     *
     * func(NaN);
     * // => false
     */
    var overEvery = createOver(arrayEvery);

    /**
     * Creates a function that checks if **any** of the `predicates` return
     * truthy when invoked with the arguments it receives.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {...(Array|Array[]|Function|Function[]|Object|Object[]|string|string[])}
     *  [predicates=[_.identity]] The predicates to check.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var func = _.overSome(Boolean, isFinite);
     *
     * func('1');
     * // => true
     *
     * func(null);
     * // => true
     *
     * func(NaN);
     * // => false
     */
    var overSome = createOver(arraySome);

    /**
     * Creates a function that returns the value at `path` of a given object.
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Util
     * @param {Array|string} path The path of the property to get.
     * @returns {Function} Returns the new accessor function.
     * @example
     *
     * var objects = [
     *   { 'a': { 'b': 2 } },
     *   { 'a': { 'b': 1 } }
     * ];
     *
     * _.map(objects, _.property('a.b'));
     * // => [2, 1]
     *
     * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
     * // => [1, 2]
     */
    function property(path) {
      return isKey(path) ? baseProperty(toKey(path)) : basePropertyDeep(path);
    }

    /**
     * The opposite of `_.property`; this method creates a function that returns
     * the value at a given path of `object`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {Object} object The object to query.
     * @returns {Function} Returns the new accessor function.
     * @example
     *
     * var array = [0, 1, 2],
     *     object = { 'a': array, 'b': array, 'c': array };
     *
     * _.map(['a[2]', 'c[0]'], _.propertyOf(object));
     * // => [2, 0]
     *
     * _.map([['a', '2'], ['c', '0']], _.propertyOf(object));
     * // => [2, 0]
     */
    function propertyOf(object) {
      return function(path) {
        return object == null ? undefined : baseGet(object, path);
      };
    }

    /**
     * Creates an array of numbers (positive and/or negative) progressing from
     * `start` up to, but not including, `end`. A step of `-1` is used if a negative
     * `start` is specified without an `end` or `step`. If `end` is not specified,
     * it's set to `start` with `start` then set to `0`.
     *
     * **Note:** JavaScript follows the IEEE-754 standard for resolving
     * floating-point values which can produce unexpected results.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {number} [start=0] The start of the range.
     * @param {number} end The end of the range.
     * @param {number} [step=1] The value to increment or decrement by.
     * @returns {Array} Returns the range of numbers.
     * @see _.inRange, _.rangeRight
     * @example
     *
     * _.range(4);
     * // => [0, 1, 2, 3]
     *
     * _.range(-4);
     * // => [0, -1, -2, -3]
     *
     * _.range(1, 5);
     * // => [1, 2, 3, 4]
     *
     * _.range(0, 20, 5);
     * // => [0, 5, 10, 15]
     *
     * _.range(0, -4, -1);
     * // => [0, -1, -2, -3]
     *
     * _.range(1, 4, 0);
     * // => [1, 1, 1]
     *
     * _.range(0);
     * // => []
     */
    var range = createRange();

    /**
     * This method is like `_.range` except that it populates values in
     * descending order.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {number} [start=0] The start of the range.
     * @param {number} end The end of the range.
     * @param {number} [step=1] The value to increment or decrement by.
     * @returns {Array} Returns the range of numbers.
     * @see _.inRange, _.range
     * @example
     *
     * _.rangeRight(4);
     * // => [3, 2, 1, 0]
     *
     * _.rangeRight(-4);
     * // => [-3, -2, -1, 0]
     *
     * _.rangeRight(1, 5);
     * // => [4, 3, 2, 1]
     *
     * _.rangeRight(0, 20, 5);
     * // => [15, 10, 5, 0]
     *
     * _.rangeRight(0, -4, -1);
     * // => [-3, -2, -1, 0]
     *
     * _.rangeRight(1, 4, 0);
     * // => [1, 1, 1]
     *
     * _.rangeRight(0);
     * // => []
     */
    var rangeRight = createRange(true);

    /**
     * Invokes the iteratee `n` times, returning an array of the results of
     * each invocation. The iteratee is invoked with one argument; (index).
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {number} n The number of times to invoke `iteratee`.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the array of results.
     * @example
     *
     * _.times(3, String);
     * // => ['0', '1', '2']
     *
     *  _.times(4, _.constant(true));
     * // => [true, true, true, true]
     */
    function times(n, iteratee) {
      n = toInteger(n);
      if (n < 1 || n > MAX_SAFE_INTEGER) {
        return [];
      }
      var index = MAX_ARRAY_LENGTH,
          length = nativeMin(n, MAX_ARRAY_LENGTH);

      iteratee = getIteratee(iteratee);
      n -= MAX_ARRAY_LENGTH;

      var result = baseTimes(length, iteratee);
      while (++index < n) {
        iteratee(index);
      }
      return result;
    }

    /**
     * Converts `value` to a property path array.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {*} value The value to convert.
     * @returns {Array} Returns the new property path array.
     * @example
     *
     * _.toPath('a.b.c');
     * // => ['a', 'b', 'c']
     *
     * _.toPath('a[0].b.c');
     * // => ['a', '0', 'b', 'c']
     *
     * var path = ['a', 'b', 'c'],
     *     newPath = _.toPath(path);
     *
     * console.log(newPath);
     * // => ['a', 'b', 'c']
     *
     * console.log(path === newPath);
     * // => false
     */
    function toPath(value) {
      if (isArray(value)) {
        return arrayMap(value, toKey);
      }
      return isSymbol(value) ? [value] : copyArray(stringToPath(value));
    }

    /**
     * Generates a unique ID. If `prefix` is given, the ID is appended to it.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {string} [prefix=''] The value to prefix the ID with.
     * @returns {string} Returns the unique ID.
     * @example
     *
     * _.uniqueId('contact_');
     * // => 'contact_104'
     *
     * _.uniqueId();
     * // => '105'
     */
    function uniqueId(prefix) {
      var id = ++idCounter;
      return toString(prefix) + id;
    }

    /*------------------------------------------------------------------------*/

    /**
     * Adds two numbers.
     *
     * @static
     * @memberOf _
     * @since 3.4.0
     * @category Math
     * @param {number} augend The first number in an addition.
     * @param {number} addend The second number in an addition.
     * @returns {number} Returns the total.
     * @example
     *
     * _.add(6, 4);
     * // => 10
     */
    var add = createMathOperation(function(augend, addend) {
      return augend + addend;
    });

    /**
     * Computes `number` rounded up to `precision`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Math
     * @param {number} number The number to round up.
     * @param {number} [precision=0] The precision to round up to.
     * @returns {number} Returns the rounded up number.
     * @example
     *
     * _.ceil(4.006);
     * // => 5
     *
     * _.ceil(6.004, 2);
     * // => 6.01
     *
     * _.ceil(6040, -2);
     * // => 6100
     */
    var ceil = createRound('ceil');

    /**
     * Divide two numbers.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Math
     * @param {number} dividend The first number in a division.
     * @param {number} divisor The second number in a division.
     * @returns {number} Returns the quotient.
     * @example
     *
     * _.divide(6, 4);
     * // => 1.5
     */
    var divide = createMathOperation(function(dividend, divisor) {
      return dividend / divisor;
    });

    /**
     * Computes `number` rounded down to `precision`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Math
     * @param {number} number The number to round down.
     * @param {number} [precision=0] The precision to round down to.
     * @returns {number} Returns the rounded down number.
     * @example
     *
     * _.floor(4.006);
     * // => 4
     *
     * _.floor(0.046, 2);
     * // => 0.04
     *
     * _.floor(4060, -2);
     * // => 4000
     */
    var floor = createRound('floor');

    /**
     * Computes the maximum value of `array`. If `array` is empty or falsey,
     * `undefined` is returned.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {*} Returns the maximum value.
     * @example
     *
     * _.max([4, 2, 8, 6]);
     * // => 8
     *
     * _.max([]);
     * // => undefined
     */
    function max(array) {
      return (array && array.length)
        ? baseExtremum(array, identity, baseGt)
        : undefined;
    }

    /**
     * This method is like `_.max` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the criterion by which
     * the value is ranked. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {*} Returns the maximum value.
     * @example
     *
     * var objects = [{ 'n': 1 }, { 'n': 2 }];
     *
     * _.maxBy(objects, function(o) { return o.n; });
     * // => { 'n': 2 }
     *
     * // The `_.property` iteratee shorthand.
     * _.maxBy(objects, 'n');
     * // => { 'n': 2 }
     */
    function maxBy(array, iteratee) {
      return (array && array.length)
        ? baseExtremum(array, getIteratee(iteratee), baseGt)
        : undefined;
    }

    /**
     * Computes the mean of the values in `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {number} Returns the mean.
     * @example
     *
     * _.mean([4, 2, 8, 6]);
     * // => 5
     */
    function mean(array) {
      return baseMean(array, identity);
    }

    /**
     * This method is like `_.mean` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the value to be averaged.
     * The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {number} Returns the mean.
     * @example
     *
     * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
     *
     * _.meanBy(objects, function(o) { return o.n; });
     * // => 5
     *
     * // The `_.property` iteratee shorthand.
     * _.meanBy(objects, 'n');
     * // => 5
     */
    function meanBy(array, iteratee) {
      return baseMean(array, getIteratee(iteratee));
    }

    /**
     * Computes the minimum value of `array`. If `array` is empty or falsey,
     * `undefined` is returned.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {*} Returns the minimum value.
     * @example
     *
     * _.min([4, 2, 8, 6]);
     * // => 2
     *
     * _.min([]);
     * // => undefined
     */
    function min(array) {
      return (array && array.length)
        ? baseExtremum(array, identity, baseLt)
        : undefined;
    }

    /**
     * This method is like `_.min` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the criterion by which
     * the value is ranked. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {*} Returns the minimum value.
     * @example
     *
     * var objects = [{ 'n': 1 }, { 'n': 2 }];
     *
     * _.minBy(objects, function(o) { return o.n; });
     * // => { 'n': 1 }
     *
     * // The `_.property` iteratee shorthand.
     * _.minBy(objects, 'n');
     * // => { 'n': 1 }
     */
    function minBy(array, iteratee) {
      return (array && array.length)
        ? baseExtremum(array, getIteratee(iteratee), baseLt)
        : undefined;
    }

    /**
     * Multiply two numbers.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Math
     * @param {number} multiplier The first number in a multiplication.
     * @param {number} multiplicand The second number in a multiplication.
     * @returns {number} Returns the product.
     * @example
     *
     * _.multiply(6, 4);
     * // => 24
     */
    var multiply = createMathOperation(function(multiplier, multiplicand) {
      return multiplier * multiplicand;
    });

    /**
     * Computes `number` rounded to `precision`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Math
     * @param {number} number The number to round.
     * @param {number} [precision=0] The precision to round to.
     * @returns {number} Returns the rounded number.
     * @example
     *
     * _.round(4.006);
     * // => 4
     *
     * _.round(4.006, 2);
     * // => 4.01
     *
     * _.round(4060, -2);
     * // => 4100
     */
    var round = createRound('round');

    /**
     * Subtract two numbers.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {number} minuend The first number in a subtraction.
     * @param {number} subtrahend The second number in a subtraction.
     * @returns {number} Returns the difference.
     * @example
     *
     * _.subtract(6, 4);
     * // => 2
     */
    var subtract = createMathOperation(function(minuend, subtrahend) {
      return minuend - subtrahend;
    });

    /**
     * Computes the sum of the values in `array`.
     *
     * @static
     * @memberOf _
     * @since 3.4.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {number} Returns the sum.
     * @example
     *
     * _.sum([4, 2, 8, 6]);
     * // => 20
     */
    function sum(array) {
      return (array && array.length)
        ? baseSum(array, identity)
        : 0;
    }

    /**
     * This method is like `_.sum` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the value to be summed.
     * The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Array|Function|Object|string} [iteratee=_.identity]
     *  The iteratee invoked per element.
     * @returns {number} Returns the sum.
     * @example
     *
     * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
     *
     * _.sumBy(objects, function(o) { return o.n; });
     * // => 20
     *
     * // The `_.property` iteratee shorthand.
     * _.sumBy(objects, 'n');
     * // => 20
     */
    function sumBy(array, iteratee) {
      return (array && array.length)
        ? baseSum(array, getIteratee(iteratee))
        : 0;
    }

    /*------------------------------------------------------------------------*/

    // Add methods that return wrapped values in chain sequences.
    lodash.after = after;
    lodash.ary = ary;
    lodash.assign = assign;
    lodash.assignIn = assignIn;
    lodash.assignInWith = assignInWith;
    lodash.assignWith = assignWith;
    lodash.at = at;
    lodash.before = before;
    lodash.bind = bind;
    lodash.bindAll = bindAll;
    lodash.bindKey = bindKey;
    lodash.castArray = castArray;
    lodash.chain = chain;
    lodash.chunk = chunk;
    lodash.compact = compact;
    lodash.concat = concat;
    lodash.cond = cond;
    lodash.conforms = conforms;
    lodash.constant = constant;
    lodash.countBy = countBy;
    lodash.create = create;
    lodash.curry = curry;
    lodash.curryRight = curryRight;
    lodash.debounce = debounce;
    lodash.defaults = defaults;
    lodash.defaultsDeep = defaultsDeep;
    lodash.defer = defer;
    lodash.delay = delay;
    lodash.difference = difference;
    lodash.differenceBy = differenceBy;
    lodash.differenceWith = differenceWith;
    lodash.drop = drop;
    lodash.dropRight = dropRight;
    lodash.dropRightWhile = dropRightWhile;
    lodash.dropWhile = dropWhile;
    lodash.fill = fill;
    lodash.filter = filter;
    lodash.flatMap = flatMap;
    lodash.flatMapDeep = flatMapDeep;
    lodash.flatMapDepth = flatMapDepth;
    lodash.flatten = flatten;
    lodash.flattenDeep = flattenDeep;
    lodash.flattenDepth = flattenDepth;
    lodash.flip = flip;
    lodash.flow = flow;
    lodash.flowRight = flowRight;
    lodash.fromPairs = fromPairs;
    lodash.functions = functions;
    lodash.functionsIn = functionsIn;
    lodash.groupBy = groupBy;
    lodash.initial = initial;
    lodash.intersection = intersection;
    lodash.intersectionBy = intersectionBy;
    lodash.intersectionWith = intersectionWith;
    lodash.invert = invert;
    lodash.invertBy = invertBy;
    lodash.invokeMap = invokeMap;
    lodash.iteratee = iteratee;
    lodash.keyBy = keyBy;
    lodash.keys = keys;
    lodash.keysIn = keysIn;
    lodash.map = map;
    lodash.mapKeys = mapKeys;
    lodash.mapValues = mapValues;
    lodash.matches = matches;
    lodash.matchesProperty = matchesProperty;
    lodash.memoize = memoize;
    lodash.merge = merge;
    lodash.mergeWith = mergeWith;
    lodash.method = method;
    lodash.methodOf = methodOf;
    lodash.mixin = mixin;
    lodash.negate = negate;
    lodash.nthArg = nthArg;
    lodash.omit = omit;
    lodash.omitBy = omitBy;
    lodash.once = once;
    lodash.orderBy = orderBy;
    lodash.over = over;
    lodash.overArgs = overArgs;
    lodash.overEvery = overEvery;
    lodash.overSome = overSome;
    lodash.partial = partial;
    lodash.partialRight = partialRight;
    lodash.partition = partition;
    lodash.pick = pick;
    lodash.pickBy = pickBy;
    lodash.property = property;
    lodash.propertyOf = propertyOf;
    lodash.pull = pull;
    lodash.pullAll = pullAll;
    lodash.pullAllBy = pullAllBy;
    lodash.pullAllWith = pullAllWith;
    lodash.pullAt = pullAt;
    lodash.range = range;
    lodash.rangeRight = rangeRight;
    lodash.rearg = rearg;
    lodash.reject = reject;
    lodash.remove = remove;
    lodash.rest = rest;
    lodash.reverse = reverse;
    lodash.sampleSize = sampleSize;
    lodash.set = set;
    lodash.setWith = setWith;
    lodash.shuffle = shuffle;
    lodash.slice = slice;
    lodash.sortBy = sortBy;
    lodash.sortedUniq = sortedUniq;
    lodash.sortedUniqBy = sortedUniqBy;
    lodash.split = split;
    lodash.spread = spread;
    lodash.tail = tail;
    lodash.take = take;
    lodash.takeRight = takeRight;
    lodash.takeRightWhile = takeRightWhile;
    lodash.takeWhile = takeWhile;
    lodash.tap = tap;
    lodash.throttle = throttle;
    lodash.thru = thru;
    lodash.toArray = toArray;
    lodash.toPairs = toPairs;
    lodash.toPairsIn = toPairsIn;
    lodash.toPath = toPath;
    lodash.toPlainObject = toPlainObject;
    lodash.transform = transform;
    lodash.unary = unary;
    lodash.union = union;
    lodash.unionBy = unionBy;
    lodash.unionWith = unionWith;
    lodash.uniq = uniq;
    lodash.uniqBy = uniqBy;
    lodash.uniqWith = uniqWith;
    lodash.unset = unset;
    lodash.unzip = unzip;
    lodash.unzipWith = unzipWith;
    lodash.update = update;
    lodash.updateWith = updateWith;
    lodash.values = values;
    lodash.valuesIn = valuesIn;
    lodash.without = without;
    lodash.words = words;
    lodash.wrap = wrap;
    lodash.xor = xor;
    lodash.xorBy = xorBy;
    lodash.xorWith = xorWith;
    lodash.zip = zip;
    lodash.zipObject = zipObject;
    lodash.zipObjectDeep = zipObjectDeep;
    lodash.zipWith = zipWith;

    // Add aliases.
    lodash.entries = toPairs;
    lodash.entriesIn = toPairsIn;
    lodash.extend = assignIn;
    lodash.extendWith = assignInWith;

    // Add methods to `lodash.prototype`.
    mixin(lodash, lodash);

    /*------------------------------------------------------------------------*/

    // Add methods that return unwrapped values in chain sequences.
    lodash.add = add;
    lodash.attempt = attempt;
    lodash.camelCase = camelCase;
    lodash.capitalize = capitalize;
    lodash.ceil = ceil;
    lodash.clamp = clamp;
    lodash.clone = clone;
    lodash.cloneDeep = cloneDeep;
    lodash.cloneDeepWith = cloneDeepWith;
    lodash.cloneWith = cloneWith;
    lodash.deburr = deburr;
    lodash.divide = divide;
    lodash.endsWith = endsWith;
    lodash.eq = eq;
    lodash.escape = escape;
    lodash.escapeRegExp = escapeRegExp;
    lodash.every = every;
    lodash.find = find;
    lodash.findIndex = findIndex;
    lodash.findKey = findKey;
    lodash.findLast = findLast;
    lodash.findLastIndex = findLastIndex;
    lodash.findLastKey = findLastKey;
    lodash.floor = floor;
    lodash.forEach = forEach;
    lodash.forEachRight = forEachRight;
    lodash.forIn = forIn;
    lodash.forInRight = forInRight;
    lodash.forOwn = forOwn;
    lodash.forOwnRight = forOwnRight;
    lodash.get = get;
    lodash.gt = gt;
    lodash.gte = gte;
    lodash.has = has;
    lodash.hasIn = hasIn;
    lodash.head = head;
    lodash.identity = identity;
    lodash.includes = includes;
    lodash.indexOf = indexOf;
    lodash.inRange = inRange;
    lodash.invoke = invoke;
    lodash.isArguments = isArguments;
    lodash.isArray = isArray;
    lodash.isArrayBuffer = isArrayBuffer;
    lodash.isArrayLike = isArrayLike;
    lodash.isArrayLikeObject = isArrayLikeObject;
    lodash.isBoolean = isBoolean;
    lodash.isBuffer = isBuffer;
    lodash.isDate = isDate;
    lodash.isElement = isElement;
    lodash.isEmpty = isEmpty;
    lodash.isEqual = isEqual;
    lodash.isEqualWith = isEqualWith;
    lodash.isError = isError;
    lodash.isFinite = isFinite;
    lodash.isFunction = isFunction;
    lodash.isInteger = isInteger;
    lodash.isLength = isLength;
    lodash.isMap = isMap;
    lodash.isMatch = isMatch;
    lodash.isMatchWith = isMatchWith;
    lodash.isNaN = isNaN;
    lodash.isNative = isNative;
    lodash.isNil = isNil;
    lodash.isNull = isNull;
    lodash.isNumber = isNumber;
    lodash.isObject = isObject;
    lodash.isObjectLike = isObjectLike;
    lodash.isPlainObject = isPlainObject;
    lodash.isRegExp = isRegExp;
    lodash.isSafeInteger = isSafeInteger;
    lodash.isSet = isSet;
    lodash.isString = isString;
    lodash.isSymbol = isSymbol;
    lodash.isTypedArray = isTypedArray;
    lodash.isUndefined = isUndefined;
    lodash.isWeakMap = isWeakMap;
    lodash.isWeakSet = isWeakSet;
    lodash.join = join;
    lodash.kebabCase = kebabCase;
    lodash.last = last;
    lodash.lastIndexOf = lastIndexOf;
    lodash.lowerCase = lowerCase;
    lodash.lowerFirst = lowerFirst;
    lodash.lt = lt;
    lodash.lte = lte;
    lodash.max = max;
    lodash.maxBy = maxBy;
    lodash.mean = mean;
    lodash.meanBy = meanBy;
    lodash.min = min;
    lodash.minBy = minBy;
    lodash.multiply = multiply;
    lodash.nth = nth;
    lodash.noConflict = noConflict;
    lodash.noop = noop;
    lodash.now = now;
    lodash.pad = pad;
    lodash.padEnd = padEnd;
    lodash.padStart = padStart;
    lodash.parseInt = parseInt;
    lodash.random = random;
    lodash.reduce = reduce;
    lodash.reduceRight = reduceRight;
    lodash.repeat = repeat;
    lodash.replace = replace;
    lodash.result = result;
    lodash.round = round;
    lodash.runInContext = runInContext;
    lodash.sample = sample;
    lodash.size = size;
    lodash.snakeCase = snakeCase;
    lodash.some = some;
    lodash.sortedIndex = sortedIndex;
    lodash.sortedIndexBy = sortedIndexBy;
    lodash.sortedIndexOf = sortedIndexOf;
    lodash.sortedLastIndex = sortedLastIndex;
    lodash.sortedLastIndexBy = sortedLastIndexBy;
    lodash.sortedLastIndexOf = sortedLastIndexOf;
    lodash.startCase = startCase;
    lodash.startsWith = startsWith;
    lodash.subtract = subtract;
    lodash.sum = sum;
    lodash.sumBy = sumBy;
    lodash.template = template;
    lodash.times = times;
    lodash.toFinite = toFinite;
    lodash.toInteger = toInteger;
    lodash.toLength = toLength;
    lodash.toLower = toLower;
    lodash.toNumber = toNumber;
    lodash.toSafeInteger = toSafeInteger;
    lodash.toString = toString;
    lodash.toUpper = toUpper;
    lodash.trim = trim;
    lodash.trimEnd = trimEnd;
    lodash.trimStart = trimStart;
    lodash.truncate = truncate;
    lodash.unescape = unescape;
    lodash.uniqueId = uniqueId;
    lodash.upperCase = upperCase;
    lodash.upperFirst = upperFirst;

    // Add aliases.
    lodash.each = forEach;
    lodash.eachRight = forEachRight;
    lodash.first = head;

    mixin(lodash, (function() {
      var source = {};
      baseForOwn(lodash, function(func, methodName) {
        if (!hasOwnProperty.call(lodash.prototype, methodName)) {
          source[methodName] = func;
        }
      });
      return source;
    }()), { 'chain': false });

    /*------------------------------------------------------------------------*/

    /**
     * The semantic version number.
     *
     * @static
     * @memberOf _
     * @type {string}
     */
    lodash.VERSION = VERSION;

    // Assign default placeholders.
    arrayEach(['bind', 'bindKey', 'curry', 'curryRight', 'partial', 'partialRight'], function(methodName) {
      lodash[methodName].placeholder = lodash;
    });

    // Add `LazyWrapper` methods for `_.drop` and `_.take` variants.
    arrayEach(['drop', 'take'], function(methodName, index) {
      LazyWrapper.prototype[methodName] = function(n) {
        var filtered = this.__filtered__;
        if (filtered && !index) {
          return new LazyWrapper(this);
        }
        n = n === undefined ? 1 : nativeMax(toInteger(n), 0);

        var result = this.clone();
        if (filtered) {
          result.__takeCount__ = nativeMin(n, result.__takeCount__);
        } else {
          result.__views__.push({
            'size': nativeMin(n, MAX_ARRAY_LENGTH),
            'type': methodName + (result.__dir__ < 0 ? 'Right' : '')
          });
        }
        return result;
      };

      LazyWrapper.prototype[methodName + 'Right'] = function(n) {
        return this.reverse()[methodName](n).reverse();
      };
    });

    // Add `LazyWrapper` methods that accept an `iteratee` value.
    arrayEach(['filter', 'map', 'takeWhile'], function(methodName, index) {
      var type = index + 1,
          isFilter = type == LAZY_FILTER_FLAG || type == LAZY_WHILE_FLAG;

      LazyWrapper.prototype[methodName] = function(iteratee) {
        var result = this.clone();
        result.__iteratees__.push({
          'iteratee': getIteratee(iteratee, 3),
          'type': type
        });
        result.__filtered__ = result.__filtered__ || isFilter;
        return result;
      };
    });

    // Add `LazyWrapper` methods for `_.head` and `_.last`.
    arrayEach(['head', 'last'], function(methodName, index) {
      var takeName = 'take' + (index ? 'Right' : '');

      LazyWrapper.prototype[methodName] = function() {
        return this[takeName](1).value()[0];
      };
    });

    // Add `LazyWrapper` methods for `_.initial` and `_.tail`.
    arrayEach(['initial', 'tail'], function(methodName, index) {
      var dropName = 'drop' + (index ? '' : 'Right');

      LazyWrapper.prototype[methodName] = function() {
        return this.__filtered__ ? new LazyWrapper(this) : this[dropName](1);
      };
    });

    LazyWrapper.prototype.compact = function() {
      return this.filter(identity);
    };

    LazyWrapper.prototype.find = function(predicate) {
      return this.filter(predicate).head();
    };

    LazyWrapper.prototype.findLast = function(predicate) {
      return this.reverse().find(predicate);
    };

    LazyWrapper.prototype.invokeMap = rest(function(path, args) {
      if (typeof path == 'function') {
        return new LazyWrapper(this);
      }
      return this.map(function(value) {
        return baseInvoke(value, path, args);
      });
    });

    LazyWrapper.prototype.reject = function(predicate) {
      predicate = getIteratee(predicate, 3);
      return this.filter(function(value) {
        return !predicate(value);
      });
    };

    LazyWrapper.prototype.slice = function(start, end) {
      start = toInteger(start);

      var result = this;
      if (result.__filtered__ && (start > 0 || end < 0)) {
        return new LazyWrapper(result);
      }
      if (start < 0) {
        result = result.takeRight(-start);
      } else if (start) {
        result = result.drop(start);
      }
      if (end !== undefined) {
        end = toInteger(end);
        result = end < 0 ? result.dropRight(-end) : result.take(end - start);
      }
      return result;
    };

    LazyWrapper.prototype.takeRightWhile = function(predicate) {
      return this.reverse().takeWhile(predicate).reverse();
    };

    LazyWrapper.prototype.toArray = function() {
      return this.take(MAX_ARRAY_LENGTH);
    };

    // Add `LazyWrapper` methods to `lodash.prototype`.
    baseForOwn(LazyWrapper.prototype, function(func, methodName) {
      var checkIteratee = /^(?:filter|find|map|reject)|While$/.test(methodName),
          isTaker = /^(?:head|last)$/.test(methodName),
          lodashFunc = lodash[isTaker ? ('take' + (methodName == 'last' ? 'Right' : '')) : methodName],
          retUnwrapped = isTaker || /^find/.test(methodName);

      if (!lodashFunc) {
        return;
      }
      lodash.prototype[methodName] = function() {
        var value = this.__wrapped__,
            args = isTaker ? [1] : arguments,
            isLazy = value instanceof LazyWrapper,
            iteratee = args[0],
            useLazy = isLazy || isArray(value);

        var interceptor = function(value) {
          var result = lodashFunc.apply(lodash, arrayPush([value], args));
          return (isTaker && chainAll) ? result[0] : result;
        };

        if (useLazy && checkIteratee && typeof iteratee == 'function' && iteratee.length != 1) {
          // Avoid lazy use if the iteratee has a "length" value other than `1`.
          isLazy = useLazy = false;
        }
        var chainAll = this.__chain__,
            isHybrid = !!this.__actions__.length,
            isUnwrapped = retUnwrapped && !chainAll,
            onlyLazy = isLazy && !isHybrid;

        if (!retUnwrapped && useLazy) {
          value = onlyLazy ? value : new LazyWrapper(this);
          var result = func.apply(value, args);
          result.__actions__.push({ 'func': thru, 'args': [interceptor], 'thisArg': undefined });
          return new LodashWrapper(result, chainAll);
        }
        if (isUnwrapped && onlyLazy) {
          return func.apply(this, args);
        }
        result = this.thru(interceptor);
        return isUnwrapped ? (isTaker ? result.value()[0] : result.value()) : result;
      };
    });

    // Add `Array` methods to `lodash.prototype`.
    arrayEach(['pop', 'push', 'shift', 'sort', 'splice', 'unshift'], function(methodName) {
      var func = arrayProto[methodName],
          chainName = /^(?:push|sort|unshift)$/.test(methodName) ? 'tap' : 'thru',
          retUnwrapped = /^(?:pop|shift)$/.test(methodName);

      lodash.prototype[methodName] = function() {
        var args = arguments;
        if (retUnwrapped && !this.__chain__) {
          var value = this.value();
          return func.apply(isArray(value) ? value : [], args);
        }
        return this[chainName](function(value) {
          return func.apply(isArray(value) ? value : [], args);
        });
      };
    });

    // Map minified method names to their real names.
    baseForOwn(LazyWrapper.prototype, function(func, methodName) {
      var lodashFunc = lodash[methodName];
      if (lodashFunc) {
        var key = (lodashFunc.name + ''),
            names = realNames[key] || (realNames[key] = []);

        names.push({ 'name': methodName, 'func': lodashFunc });
      }
    });

    realNames[createHybridWrapper(undefined, BIND_KEY_FLAG).name] = [{
      'name': 'wrapper',
      'func': undefined
    }];

    // Add methods to `LazyWrapper`.
    LazyWrapper.prototype.clone = lazyClone;
    LazyWrapper.prototype.reverse = lazyReverse;
    LazyWrapper.prototype.value = lazyValue;

    // Add chain sequence methods to the `lodash` wrapper.
    lodash.prototype.at = wrapperAt;
    lodash.prototype.chain = wrapperChain;
    lodash.prototype.commit = wrapperCommit;
    lodash.prototype.next = wrapperNext;
    lodash.prototype.plant = wrapperPlant;
    lodash.prototype.reverse = wrapperReverse;
    lodash.prototype.toJSON = lodash.prototype.valueOf = lodash.prototype.value = wrapperValue;

    if (iteratorSymbol) {
      lodash.prototype[iteratorSymbol] = wrapperToIterator;
    }
    return lodash;
  }

  /*--------------------------------------------------------------------------*/

  // Export lodash.
  var _ = runInContext();

  // Expose Lodash on the free variable `window` or `self` when available so it's
  // globally accessible, even when bundled with Browserify, Webpack, etc. This
  // also prevents errors in cases where Lodash is loaded by a script tag in the
  // presence of an AMD loader. See http://requirejs.org/docs/errors.html#mismatch
  // for more details. Use `_.noConflict` to remove Lodash from the global object.
  (freeWindow || freeSelf || {})._ = _;

  // Some AMD build optimizers like r.js check for condition patterns like the following:
  if (typeof define == 'function' && typeof define.amd == 'object' && define.amd) {
    // Define as an anonymous module so, through path mapping, it can be
    // referenced as the "underscore" module.
    define(function() {
      return _;
    });
  }
  // Check for `exports` after `define` in case a build optimizer adds an `exports` object.
  else if (freeExports && freeModule) {
    // Export for Node.js.
    if (moduleExports) {
      (freeModule.exports = _)._ = _;
    }
    // Export for CommonJS support.
    freeExports._ = _;
  }
  else {
    // Export to the global object.
    root._ = _;
  }
}.call(this));

/**!
 * AngularJS file upload directives and services. Supoorts: file upload/drop/paste, resume, cancel/abort,
 * progress, resize, thumbnail, preview, validation and CORS
 * @author  Danial  <danial.farid@gmail.com>
 * @version 10.0.2
 */

if (window.XMLHttpRequest && !(window.FileAPI && FileAPI.shouldLoad)) {
  window.XMLHttpRequest.prototype.setRequestHeader = (function (orig) {
    return function (header, value) {
      if (header === '__setXHR_') {
        var val = value(this);
        // fix for angular < 1.2.0
        if (val instanceof Function) {
          val(this);
        }
      } else {
        orig.apply(this, arguments);
      }
    };
  })(window.XMLHttpRequest.prototype.setRequestHeader);
}

var ngFileUpload = angular.module('ngFileUpload', []);

ngFileUpload.version = '10.0.2';

ngFileUpload.service('UploadBase', ['$http', '$q', '$timeout', function ($http, $q, $timeout) {
  var upload = this;

  this.isResumeSupported = function () {
    return window.Blob && (Blob instanceof Function) && new Blob().slice;
  };

  var resumeSupported = this.isResumeSupported();

  function sendHttp(config) {
    config.method = config.method || 'POST';
    config.headers = config.headers || {};

    var deferred = config._deferred = config._deferred || $q.defer();
    var promise = deferred.promise;

    function notifyProgress(e) {
      if (deferred.notify) {
        deferred.notify(e);
      }
      if (promise.progressFunc) {
        $timeout(function () {
          promise.progressFunc(e);
        });
      }
    }

    function getNotifyEvent(n) {
      if (config._start != null && resumeSupported) {
        return {
          loaded: n.loaded + config._start, total: config._file.size, type: n.type, config: config,
          lengthComputable: true, target: n.target
        };
      } else {
        return n;
      }
    }

    if (!config.disableProgress) {
      config.headers.__setXHR_ = function () {
        return function (xhr) {
          if (!xhr || !(xhr instanceof XMLHttpRequest)) return;
          config.__XHR = xhr;
          if (config.xhrFn) config.xhrFn(xhr);
          xhr.upload.addEventListener('progress', function (e) {
            e.config = config;
            notifyProgress(getNotifyEvent(e));
          }, false);
          //fix for firefox not firing upload progress end, also IE8-9
          xhr.upload.addEventListener('load', function (e) {
            if (e.lengthComputable) {
              e.config = config;
              notifyProgress(getNotifyEvent(e));
            }
          }, false);
        };
      };
    }

    function uploadWithAngular() {
      $http(config).then(function (r) {
        if (resumeSupported && config._chunkSize && !config._finished) {
          notifyProgress({loaded: config._end, total: config._file.size, config: config, type: 'progress'});
          upload.upload(config);
        } else {
          if (config._finished) delete config._finished;
          deferred.resolve(r);
        }
      }, function (e) {
        deferred.reject(e);
      }, function (n) {
        deferred.notify(n);
      });
    }

    if (!resumeSupported) {
      uploadWithAngular();
    } else if (config._chunkSize && config._end && !config._finished) {
      config._start = config._end;
      config._end += config._chunkSize;
      uploadWithAngular();
    } else if (config.resumeSizeUrl) {
      $http.get(config.resumeSizeUrl).then(function (resp) {
        if (config.resumeSizeResponseReader) {
          config._start = config.resumeSizeResponseReader(resp.data);
        } else {
          config._start = parseInt((resp.data.size == null ? resp.data : resp.data.size).toString());
        }
        if (config._chunkSize) {
          config._end = config._start + config._chunkSize;
        }
        uploadWithAngular();
      }, function (e) {
        throw e;
      });
    } else if (config.resumeSize) {
      config.resumeSize().then(function (size) {
        config._start = size;
        uploadWithAngular();
      }, function (e) {
        throw e;
      });
    } else {
      uploadWithAngular();
    }


    promise.success = function (fn) {
      promise.then(function (response) {
        fn(response.data, response.status, response.headers, config);
      });
      return promise;
    };

    promise.error = function (fn) {
      promise.then(null, function (response) {
        fn(response.data, response.status, response.headers, config);
      });
      return promise;
    };

    promise.progress = function (fn) {
      promise.progressFunc = fn;
      promise.then(null, null, function (n) {
        fn(n);
      });
      return promise;
    };
    promise.abort = promise.pause = function () {
      if (config.__XHR) {
        $timeout(function () {
          config.__XHR.abort();
        });
      }
      return promise;
    };
    promise.xhr = function (fn) {
      config.xhrFn = (function (origXhrFn) {
        return function () {
          if (origXhrFn) origXhrFn.apply(promise, arguments);
          fn.apply(promise, arguments);
        };
      })(config.xhrFn);
      return promise;
    };

    return promise;
  }

  this.rename = function (file, name) {
    file.ngfName = name;
    return file;
  };

  this.jsonBlob = function (val) {
    if (val != null && !angular.isString(val)) {
      val = JSON.stringify(val);
    }
    var blob = new Blob([val], {type: 'application/json'});
    blob._ngfBlob = true;
    return blob;
  };

  this.json = function (val) {
    return angular.toJson(val);
  };

  this.upload = function (config) {
    function isFile(file) {
      return file != null && (file instanceof Blob || (file.flashId && file.name && file.size));
    }

    function toResumeFile(file, formData) {
      if (file._ngfBlob) return file;
      config._file = config._file || file;
      if (config._start != null && resumeSupported) {
        if (config._end && config._end >= file.size) {
          config._finished = true;
          config._end = file.size;
        }
        var slice = file.slice(config._start, config._end || file.size);
        slice.name = file.name;
        slice.ngfName = file.ngfName;
        if (config._chunkSize) {
          formData.append('_chunkSize', config._end - config._start);
          formData.append('_chunkNumber', Math.floor(config._start / config._chunkSize));
          formData.append('_totalSize', config._file.size);
        }
        return slice;
      }
      return file;
    }

    function addFieldToFormData(formData, val, key) {
      if (val !== undefined) {
        if (angular.isDate(val)) {
          val = val.toISOString();
        }
        if (angular.isString(val)) {
          formData.append(key, val);
        } else if (isFile(val)) {
          var file = toResumeFile(val, formData);
          var split = key.split(',');
          if (split[1]) {
            file.ngfName = split[1].replace(/^\s+|\s+$/g, '');
            key = split[0];
          }
          config._fileKey = config._fileKey || key;
          formData.append(key, file, file.ngfName || file.name);
        } else {
          if (angular.isObject(val)) {
            if (val.$$ngfCircularDetection) throw 'ngFileUpload: Circular reference in config.data. Make sure specified data for Upload.upload() has no circular reference: ' + key;

            val.$$ngfCircularDetection = true;
            try {
              for (var k in val) {
                if (val.hasOwnProperty(k) && k !== '$$ngfCircularDetection') {
                  var objectKey = config.objectKey == null ? '[i]' : config.objectKey;
                  if (val.length && parseInt(k) > -1) {
                    objectKey = config.arrayKey == null ? objectKey : config.arrayKey;
                  }
                  addFieldToFormData(formData, val[k], key + objectKey.replace(/[ik]/g, k));
                }
              }
            } finally {
              delete val.$$ngfCircularDetection;
            }
          } else {
            formData.append(key, val);
          }
        }
      }
    }

    function digestConfig() {
      config._chunkSize = upload.translateScalars(config.resumeChunkSize);
      config._chunkSize = config._chunkSize ? parseInt(config._chunkSize.toString()) : null;

      config.headers = config.headers || {};
      config.headers['Content-Type'] = undefined;
      config.transformRequest = config.transformRequest ?
        (angular.isArray(config.transformRequest) ?
          config.transformRequest : [config.transformRequest]) : [];
      config.transformRequest.push(function (data) {
        var formData = new FormData(), key;
        data = data || config.fields || {};
        if (config.file) {
          data.file = config.file;
        }
        for (key in data) {
          if (data.hasOwnProperty(key)) {
            var val = data[key];
            if (config.formDataAppender) {
              config.formDataAppender(formData, key, val);
            } else {
              addFieldToFormData(formData, val, key);
            }
          }
        }

        return formData;
      });
    }

    if (!config._isDigested) {
      config._isDigested = true;
      digestConfig();
    }

    return sendHttp(config);
  };

  this.http = function (config) {
    config.transformRequest = config.transformRequest || function (data) {
        if ((window.ArrayBuffer && data instanceof window.ArrayBuffer) || data instanceof Blob) {
          return data;
        }
        return $http.defaults.transformRequest[0].apply(this, arguments);
      };
    config._chunkSize = upload.translateScalars(config.resumeChunkSize);
    config._chunkSize = config._chunkSize ? parseInt(config._chunkSize.toString()) : null;

    return sendHttp(config);
  };

  this.translateScalars = function (str) {
    if (angular.isString(str)) {
      if (str.search(/kb/i) === str.length - 2) {
        return parseFloat(str.substring(0, str.length - 2) * 1000);
      } else if (str.search(/mb/i) === str.length - 2) {
        return parseFloat(str.substring(0, str.length - 2) * 1000000);
      } else if (str.search(/gb/i) === str.length - 2) {
        return parseFloat(str.substring(0, str.length - 2) * 1000000000);
      } else if (str.search(/b/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1));
      } else if (str.search(/s/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1));
      } else if (str.search(/m/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1) * 60);
      } else if (str.search(/h/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1) * 3600);
      }
    }
    return str;
  };

  this.setDefaults = function (defaults) {
    this.defaults = defaults || {};
  };

  this.defaults = {};
  this.version = ngFileUpload.version;
}

]);

ngFileUpload.service('Upload', ['$parse', '$timeout', '$compile', '$q', 'UploadExif', function ($parse, $timeout, $compile, $q, UploadExif) {
  var upload = UploadExif;
  upload.getAttrWithDefaults = function (attr, name) {
    if (attr[name] != null) return attr[name];
    var def = upload.defaults[name];
    return (def == null ? def : (angular.isString(def) ? def : JSON.stringify(def)));
  };

  upload.attrGetter = function (name, attr, scope, params) {
    var attrVal = this.getAttrWithDefaults(attr, name);
    if (scope) {
      try {
        if (params) {
          return $parse(attrVal)(scope, params);
        } else {
          return $parse(attrVal)(scope);
        }
      } catch (e) {
        // hangle string value without single qoute
        if (name.search(/min|max|pattern/i)) {
          return attrVal;
        } else {
          throw e;
        }
      }
    } else {
      return attrVal;
    }
  };

  upload.shouldUpdateOn = function (type, attr, scope) {
    var modelOptions = upload.attrGetter('ngModelOptions', attr, scope);
    if (modelOptions && modelOptions.updateOn) {
      return modelOptions.updateOn.split(' ').indexOf(type) > -1;
    }
    return true;
  };

  upload.emptyPromise = function () {
    var d = $q.defer();
    var args = arguments;
    $timeout(function () {
      d.resolve.apply(d, args);
    });
    return d.promise;
  };

  upload.happyPromise = function (promise, data) {
    var d = $q.defer();
    promise.then(function (result) {
      d.resolve(result);
    }, function (error) {
      $timeout(function () {
        throw error;
      });
      d.resolve(data);
    });
    return d.promise;
  };

  function applyExifRotations(files, attr, scope) {
    var promises = [upload.emptyPromise()];
    angular.forEach(files, function (f, i) {
      if (f.type.indexOf('image/jpeg') === 0 && upload.attrGetter('ngfFixOrientation', attr, scope, {$file: f})) {
        promises.push(upload.happyPromise(upload.applyExifRotation(f), f).then(function (fixedFile) {
          files.splice(i, 1, fixedFile);
        }));
      }
    });
    return $q.all(promises);
  }

  function resize(files, attr, scope) {
    var param = upload.attrGetter('ngfResize', attr, scope);
    if (!param || !upload.isResizeSupported() || !files.length) return upload.emptyPromise();
    var promises = [upload.emptyPromise()];
    angular.forEach(files, function (f, i) {
      if (f.type.indexOf('image') === 0) {
        var promise = upload.resize(f, param.width, param.height, param.quality, param.type);
        promises.push(promise);
        promise.then(function (resizedFile) {
          files.splice(i, 1, resizedFile);
        }, function (e) {
          f.$error = 'resize';
          f.$errorParam = (e ? (e.message ? e.message : e) + ': ' : '') + (f && f.name);
        });
      }
    });
    return $q.all(promises);
  }

  function handleKeep(files, prevFiles, attr, scope) {
    var dupFiles = [];
    var keep = upload.attrGetter('ngfKeep', attr, scope);
    if (keep) {
      var hasNew = false;

      if (keep === 'distinct' || upload.attrGetter('ngfKeepDistinct', attr, scope) === true) {
        var len = prevFiles.length;
        if (files) {
          for (var i = 0; i < files.length; i++) {
            for (var j = 0; j < len; j++) {
              if (files[i].name === prevFiles[j].name) {
                dupFiles.push(files[i]);
                break;
              }
            }
            if (j === len) {
              prevFiles.push(files[i]);
              hasNew = true;
            }
          }
        }
        files = prevFiles;
      } else {
        files = prevFiles.concat(files || []);
      }
    }
    return {files: files, dupFiles: dupFiles, keep: keep};
  }

  upload.updateModel = function (ngModel, attr, scope, fileChange, files, evt, noDelay) {
    function update(files, invalidFiles, newFiles, dupFiles, isSingleModel) {
      var file = files && files.length ? files[0] : null;

      if (ngModel) {
        upload.applyModelValidation(ngModel, files);
        ngModel.$ngfModelChange = true;
        ngModel.$setViewValue(isSingleModel ? file : files);
      }

      if (fileChange) {
        $parse(fileChange)(scope, {
          $files: files,
          $file: file,
          $newFiles: newFiles,
          $duplicateFiles: dupFiles,
          $invalidFiles: invalidFiles,
          $event: evt
        });
      }

      var invalidModel = upload.attrGetter('ngfModelInvalid', attr);
      if (invalidModel) {
        $timeout(function () {
          $parse(invalidModel).assign(scope, invalidFiles);
        });
      }
      $timeout(function () {
        // scope apply changes
      });
    }

    var newFiles = files;
    var prevFiles = ((ngModel && ngModel.$modelValue) || attr.$$ngfPrevFiles || []).slice(0);
    var keepResult = handleKeep(files, prevFiles, attr, scope);
    files = keepResult.files;
    var dupFiles = keepResult.dupFiles;
    var isSingleModel = !upload.attrGetter('ngfMultiple', attr, scope) && !upload.attrGetter('multiple', attr) && !keepResult.keep;

    attr.$$ngfPrevFiles = files;

    if (keepResult.keep && (!newFiles || !newFiles.length)) return;

    upload.validate(newFiles, ngModel, attr, scope).then(function () {
      if (noDelay) {
        update(files, [], newFiles, dupFiles, isSingleModel);
      } else {
        var options = upload.attrGetter('ngModelOptions', attr, scope);
        if (!options || !options.allowInvalid) {
          var valids = [], invalids = [];
          angular.forEach(files, function (file) {
            if (file.$error) {
              invalids.push(file);
            } else {
              valids.push(file);
            }
          });
          files = valids;
        }
        var fixOrientation = upload.emptyPromise(files);
        if (upload.attrGetter('ngfFixOrientation', attr, scope) && upload.isExifSupported()) {
          fixOrientation = applyExifRotations(files, attr, scope);
        }
        fixOrientation.then(function () {
          resize(files, attr, scope).then(function () {
            $timeout(function () {
              update(files, invalids, newFiles, dupFiles, isSingleModel);
            }, options && options.debounce ? options.debounce.change || options.debounce : 0);
          }, function (e) {
            throw 'Could not resize files ' + e;
          });
        });
      }
    });

    // cleaning object url memories
    var l = prevFiles.length;
    while (l--) {
      var prevFile = prevFiles[l];
      if (window.URL && prevFile.blobUrl) {
        URL.revokeObjectURL(prevFile.blobUrl);
        delete prevFile.blobUrl;
      }
    }
  };

  return upload;
}]);

ngFileUpload.directive('ngfSelect', ['$parse', '$timeout', '$compile', 'Upload', function ($parse, $timeout, $compile, Upload) {
  var generatedElems = [];

  function isDelayedClickSupported(ua) {
    // fix for android native browser < 4.4 and safari windows
    var m = ua.match(/Android[^\d]*(\d+)\.(\d+)/);
    if (m && m.length > 2) {
      var v = Upload.defaults.androidFixMinorVersion || 4;
      return parseInt(m[1]) < 4 || (parseInt(m[1]) === v && parseInt(m[2]) < v);
    }

    // safari on windows
    return ua.indexOf('Chrome') === -1 && /.*Windows.*Safari.*/.test(ua);
  }

  function linkFileSelect(scope, elem, attr, ngModel, $parse, $timeout, $compile, upload) {
    /** @namespace attr.ngfSelect */
    /** @namespace attr.ngfChange */
    /** @namespace attr.ngModel */
    /** @namespace attr.ngModelOptions */
    /** @namespace attr.ngfMultiple */
    /** @namespace attr.ngfCapture */
    /** @namespace attr.ngfValidate */
    /** @namespace attr.ngfKeep */
    var attrGetter = function (name, scope) {
      return upload.attrGetter(name, attr, scope);
    };

    function isInputTypeFile() {
      return elem[0].tagName.toLowerCase() === 'input' && attr.type && attr.type.toLowerCase() === 'file';
    }

    function fileChangeAttr() {
      return attrGetter('ngfChange') || attrGetter('ngfSelect');
    }

    function changeFn(evt) {
      if (upload.shouldUpdateOn('change', attr, scope)) {
        var fileList = evt.__files_ || (evt.target && evt.target.files), files = [];
        for (var i = 0; i < fileList.length; i++) {
          files.push(fileList[i]);
        }
        upload.updateModel(ngModel, attr, scope, fileChangeAttr(),
          files.length ? files : null, evt);
      }
    }

    upload.registerModelChangeValidator(ngModel, attr, scope);

    var unwatches = [];
    unwatches.push(scope.$watch(attrGetter('ngfMultiple'), function () {
      fileElem.attr('multiple', attrGetter('ngfMultiple', scope));
    }));
    unwatches.push(scope.$watch(attrGetter('ngfCapture'), function () {
      fileElem.attr('capture', attrGetter('ngfCapture', scope));
    }));
    attr.$observe('accept', function () {
      fileElem.attr('accept', attrGetter('accept'));
    });
    unwatches.push(function () {
      if (attr.$$observers) delete attr.$$observers.accept;
    });
    function bindAttrToFileInput(fileElem) {
      if (elem !== fileElem) {
        for (var i = 0; i < elem[0].attributes.length; i++) {
          var attribute = elem[0].attributes[i];
          if (attribute.name !== 'type' && attribute.name !== 'class' &&
            attribute.name !== 'id' && attribute.name !== 'style') {
            if (attribute.value == null || attribute.value === '') {
              if (attribute.name === 'required') attribute.value = 'required';
              if (attribute.name === 'multiple') attribute.value = 'multiple';
            }
            fileElem.attr(attribute.name, attribute.value);
          }
        }
      }
    }

    function createFileInput() {
      if (isInputTypeFile()) {
        return elem;
      }

      var fileElem = angular.element('<input type="file">');
      bindAttrToFileInput(fileElem);

      fileElem.css('visibility', 'hidden').css('position', 'absolute').css('overflow', 'hidden')
        .css('width', '0px').css('height', '0px').css('border', 'none')
        .css('margin', '0px').css('padding', '0px').attr('tabindex', '-1');
      generatedElems.push({el: elem, ref: fileElem});
      document.body.appendChild(fileElem[0]);

      return fileElem;
    }

    var initialTouchStartY = 0;

    function clickHandler(evt) {
      if (elem.attr('disabled') || attrGetter('ngfSelectDisabled', scope)) return false;

      var r = handleTouch(evt);
      if (r != null) return r;

      resetModel(evt);

      // fix for md when the element is removed from the DOM and added back #460
      try {
        if (!isInputTypeFile() && !document.body.contains(fileElem[0])) {
          generatedElems.push({el: elem, ref: fileElem});
          document.body.appendChild(fileElem[0]);
          fileElem.bind('change', changeFn);
        }
      } catch(e){/*ignore*/}

      if (isDelayedClickSupported(navigator.userAgent)) {
        setTimeout(function () {
          fileElem[0].click();
        }, 0);
      } else {
        fileElem[0].click();
      }

      return false;
    }

    function handleTouch(evt) {
      var touches = evt.changedTouches || (evt.originalEvent && evt.originalEvent.changedTouches);
      if (evt.type === 'touchstart') {
        initialTouchStartY = touches ? touches[0].clientY : 0;
        return true; // don't block event default
      } else {
        evt.stopPropagation();
        evt.preventDefault();

        // prevent scroll from triggering event
        if (evt.type === 'touchend') {
          var currentLocation = touches ? touches[0].clientY : 0;
          if (Math.abs(currentLocation - initialTouchStartY) > 20) return false;
        }
      }
    }

    var fileElem = elem;

    function resetModel(evt) {
      if (upload.shouldUpdateOn('click', attr, scope) && fileElem.val()) {
        fileElem.val(null);
        upload.updateModel(ngModel, attr, scope, fileChangeAttr(), null, evt, true);
      }
    }

    if (!isInputTypeFile()) {
      fileElem = createFileInput();
    }
    fileElem.bind('change', changeFn);

    if (!isInputTypeFile()) {
      elem.bind('click touchstart touchend', clickHandler);
    } else {
      elem.bind('click', resetModel);
    }

    function ie10SameFileSelectFix(evt) {
      if (fileElem && !fileElem.attr('__ngf_ie10_Fix_')) {
        if (!fileElem[0].parentNode) {
          fileElem = null;
          return;
        }
        evt.preventDefault();
        evt.stopPropagation();
        fileElem.unbind('click');
        var clone = fileElem.clone();
        fileElem.replaceWith(clone);
        fileElem = clone;
        fileElem.attr('__ngf_ie10_Fix_', 'true');
        fileElem.bind('change', changeFn);
        fileElem.bind('click', ie10SameFileSelectFix);
        fileElem[0].click();
        return false;
      } else {
        fileElem.removeAttr('__ngf_ie10_Fix_');
      }
    }

    if (navigator.appVersion.indexOf('MSIE 10') !== -1) {
      fileElem.bind('click', ie10SameFileSelectFix);
    }

    if (ngModel) ngModel.$formatters.push(function (val) {
      if (val == null || val.length === 0) {
        if (fileElem.val()) {
          fileElem.val(null);
        }
      }
      return val;
    });

    scope.$on('$destroy', function () {
      if (!isInputTypeFile()) fileElem.remove();
      angular.forEach(unwatches, function (unwatch) {
        unwatch();
      });
    });

    $timeout(function () {
      for (var i = 0; i < generatedElems.length; i++) {
        var g = generatedElems[i];
        if (!document.body.contains(g.el[0])) {
          generatedElems.splice(i, 1);
          g.ref.remove();
        }
      }
    });

    if (window.FileAPI && window.FileAPI.ngfFixIE) {
      window.FileAPI.ngfFixIE(elem, fileElem, changeFn);
    }
  }

  return {
    restrict: 'AEC',
    require: '?ngModel',
    link: function (scope, elem, attr, ngModel) {
      linkFileSelect(scope, elem, attr, ngModel, $parse, $timeout, $compile, Upload);
    }
  };
}]);

(function () {

  ngFileUpload.service('UploadDataUrl', ['UploadBase', '$timeout', '$q', function (UploadBase, $timeout, $q) {
    var upload = UploadBase;
    upload.base64DataUrl = function (file) {
      if (angular.isArray(file)) {
        var d = $q.defer(), count = 0;
        angular.forEach(file, function (f) {
          upload.dataUrl(f, true)['finally'](function () {
            count++;
            if (count === file.length) {
              var urls = [];
              angular.forEach(file, function (ff) {
                urls.push(ff.$ngfDataUrl);
              });
              d.resolve(urls, file);
            }
          });
        });
        return d.promise;
      } else {
        return upload.dataUrl(file, true);
      }
    };
    upload.dataUrl = function (file, disallowObjectUrl) {
      if (!file) return upload.emptyPromise(file, file);
      if ((disallowObjectUrl && file.$ngfDataUrl != null) || (!disallowObjectUrl && file.$ngfBlobUrl != null)) {
        return upload.emptyPromise(disallowObjectUrl ? file.$ngfDataUrl : file.$ngfBlobUrl, file);
      }
      var p = disallowObjectUrl ? file.$$ngfDataUrlPromise : file.$$ngfBlobUrlPromise;
      if (p) return p;

      var deferred = $q.defer();
      $timeout(function () {
        if (window.FileReader && file &&
          (!window.FileAPI || navigator.userAgent.indexOf('MSIE 8') === -1 || file.size < 20000) &&
          (!window.FileAPI || navigator.userAgent.indexOf('MSIE 9') === -1 || file.size < 4000000)) {
          //prefer URL.createObjectURL for handling refrences to files of all sizes
          //since it doesn´t build a large string in memory
          var URL = window.URL || window.webkitURL;
          if (URL && URL.createObjectURL && !disallowObjectUrl) {
            var url;
            try {
              url = URL.createObjectURL(file);
            } catch (e) {
              $timeout(function () {
                file.$ngfBlobUrl = '';
                deferred.reject();
              });
              return;
            }
            $timeout(function () {
              file.$ngfBlobUrl = url;
              if (url) deferred.resolve(url, file);
            });
          } else {
            var fileReader = new FileReader();
            fileReader.onload = function (e) {
              $timeout(function () {
                file.$ngfDataUrl = e.target.result;
                deferred.resolve(e.target.result, file);
              });
            };
            fileReader.onerror = function () {
              $timeout(function () {
                file.$ngfDataUrl = '';
                deferred.reject();
              });
            };
            fileReader.readAsDataURL(file);
          }
        } else {
          $timeout(function () {
            file[disallowObjectUrl ? 'dataUrl' : 'blobUrl'] = '';
            deferred.reject();
          });
        }
      });

      if (disallowObjectUrl) {
        p = file.$$ngfDataUrlPromise = deferred.promise;
      } else {
        p = file.$$ngfBlobUrlPromise = deferred.promise;
      }
      p['finally'](function () {
        delete file[disallowObjectUrl ? '$$ngfDataUrlPromise' : '$$ngfBlobUrlPromise'];
      });
      return p;
    };
    return upload;
  }]);

  function getTagType(el) {
    if (el.tagName.toLowerCase() === 'img') return 'image';
    if (el.tagName.toLowerCase() === 'audio') return 'audio';
    if (el.tagName.toLowerCase() === 'video') return 'video';
    return /./;
  }

  function linkFileDirective(Upload, $timeout, scope, elem, attr, directiveName, resizeParams, isBackground) {
    function constructDataUrl(file) {
      var disallowObjectUrl = Upload.attrGetter('ngfNoObjectUrl', attr, scope);
      Upload.dataUrl(file, disallowObjectUrl)['finally'](function () {
        $timeout(function () {
          var src = (disallowObjectUrl ? file.$ngfDataUrl : file.$ngfBlobUrl) || file.$ngfDataUrl;
          if (isBackground) {
            elem.css('background-image', 'url(\'' + (src || '') + '\')');
          } else {
            elem.attr('src', src);
          }
          if (src) {
            elem.removeClass('ng-hide');
          } else {
            elem.addClass('ng-hide');
          }
        });
      });
    }

    $timeout(function () {
      var unwatch = scope.$watch(attr[directiveName], function (file) {
        var size = resizeParams;
        if (directiveName === 'ngfThumbnail') {
          if (!size) {
            size = {width: elem[0].clientWidth, height: elem[0].clientHeight};
          }
          if (size.width === 0 && window.getComputedStyle) {
            var style = getComputedStyle(elem[0]);
            size = {
              width: parseInt(style.width.slice(0, -2)),
              height: parseInt(style.height.slice(0, -2))
            };
          }
        }

        if (angular.isString(file)) {
          elem.removeClass('ng-hide');
          if (isBackground) {
            return elem.css('background-image', 'url(\'' + file + '\')');
          } else {
            return elem.attr('src', file);
          }
        }
        if (file && file.type && file.type.search(getTagType(elem[0])) === 0 &&
          (!isBackground || file.type.indexOf('image') === 0)) {
          if (size && Upload.isResizeSupported()) {
            Upload.resize(file, size.width, size.height, size.quality).then(
              function (f) {
                constructDataUrl(f);
              }, function (e) {
                throw e;
              }
            );
          } else {
            constructDataUrl(file);
          }
        } else {
          elem.addClass('ng-hide');
        }
      });

      scope.$on('$destroy', function () {
        unwatch();
      });
    });
  }


  /** @namespace attr.ngfSrc */
  /** @namespace attr.ngfNoObjectUrl */
  ngFileUpload.directive('ngfSrc', ['Upload', '$timeout', function (Upload, $timeout) {
    return {
      restrict: 'AE',
      link: function (scope, elem, attr) {
        linkFileDirective(Upload, $timeout, scope, elem, attr, 'ngfSrc',
          Upload.attrGetter('ngfResize', attr, scope), false);
      }
    };
  }]);

  /** @namespace attr.ngfBackground */
  /** @namespace attr.ngfNoObjectUrl */
  ngFileUpload.directive('ngfBackground', ['Upload', '$timeout', function (Upload, $timeout) {
    return {
      restrict: 'AE',
      link: function (scope, elem, attr) {
        linkFileDirective(Upload, $timeout, scope, elem, attr, 'ngfBackground',
          Upload.attrGetter('ngfResize', attr, scope), true);
      }
    };
  }]);

  /** @namespace attr.ngfThumbnail */
  /** @namespace attr.ngfAsBackground */
  /** @namespace attr.ngfSize */
  /** @namespace attr.ngfNoObjectUrl */
  ngFileUpload.directive('ngfThumbnail', ['Upload', '$timeout', function (Upload, $timeout) {
    return {
      restrict: 'AE',
      link: function (scope, elem, attr) {
        var size = Upload.attrGetter('ngfSize', attr, scope);
        linkFileDirective(Upload, $timeout, scope, elem, attr, 'ngfThumbnail', size,
          Upload.attrGetter('ngfAsBackground', attr, scope));
      }
    };
  }]);

  ngFileUpload.config(['$compileProvider', function ($compileProvider) {
    if ($compileProvider.imgSrcSanitizationWhitelist) $compileProvider.imgSrcSanitizationWhitelist(/^\s*(https?|ftp|mailto|tel|local|file|data|blob):/);
    if ($compileProvider.aHrefSanitizationWhitelist) $compileProvider.aHrefSanitizationWhitelist(/^\s*(https?|ftp|mailto|tel|local|file|data|blob):/);
  }]);

  ngFileUpload.filter('ngfDataUrl', ['UploadDataUrl', '$sce', function (UploadDataUrl, $sce) {
    return function (file, disallowObjectUrl, trustedUrl) {
      if (angular.isString(file)) {
        return $sce.trustAsResourceUrl(file);
      }
      var src = file && ((disallowObjectUrl ? file.$ngfDataUrl : file.$ngfBlobUrl) || file.$ngfDataUrl);
      if (file && !src) {
        if (!file.$ngfDataUrlFilterInProgress && angular.isObject(file)) {
          file.$ngfDataUrlFilterInProgress = true;
          UploadDataUrl.dataUrl(file, disallowObjectUrl);
        }
        return '';
      }
      if (file) delete file.$ngfDataUrlFilterInProgress;
      return (file && src ? (trustedUrl ? $sce.trustAsResourceUrl(src) : src) : file) || '';
    };
  }]);

})();

ngFileUpload.service('UploadValidate', ['UploadDataUrl', '$q', '$timeout', function (UploadDataUrl, $q, $timeout) {
  var upload = UploadDataUrl;

  function globStringToRegex(str) {
    var regexp = '', excludes = [];
    if (str.length > 2 && str[0] === '/' && str[str.length - 1] === '/') {
      regexp = str.substring(1, str.length - 1);
    } else {
      var split = str.split(',');
      if (split.length > 1) {
        for (var i = 0; i < split.length; i++) {
          var r = globStringToRegex(split[i]);
          if (r.regexp) {
            regexp += '(' + r.regexp + ')';
            if (i < split.length - 1) {
              regexp += '|';
            }
          } else {
            excludes = excludes.concat(r.excludes);
          }
        }
      } else {
        if (str.indexOf('!') === 0) {
          excludes.push('^((?!' + globStringToRegex(str.substring(1)).regexp + ').)*$');
        } else {
          if (str.indexOf('.') === 0) {
            str = '*' + str;
          }
          regexp = '^' + str.replace(new RegExp('[.\\\\+*?\\[\\^\\]$(){}=!<>|:\\-]', 'g'), '\\$&') + '$';
          regexp = regexp.replace(/\\\*/g, '.*').replace(/\\\?/g, '.');
        }
      }
    }
    return {regexp: regexp, excludes: excludes};
  }

  upload.validatePattern = function (file, val) {
    if (!val) {
      return true;
    }
    var pattern = globStringToRegex(val), valid = true;
    if (pattern.regexp && pattern.regexp.length) {
      var regexp = new RegExp(pattern.regexp, 'i');
      valid = (file.type != null && regexp.test(file.type)) ||
        (file.name != null && regexp.test(file.name));
    }
    var len = pattern.excludes.length;
    while (len--) {
      var exclude = new RegExp(pattern.excludes[len], 'i');
      valid = valid && (file.type == null || exclude.test(file.type)) &&
        (file.name == null || exclude.test(file.name));
    }
    return valid;
  };

  upload.registerModelChangeValidator = function (ngModel, attr, scope) {
    if (ngModel) {
      ngModel.$formatters.push(function (files) {
        if (!ngModel.$ngfModelChange) {
          upload.validate(files, ngModel, attr, scope, function () {
            upload.applyModelValidation(ngModel, files);
          });
        } else {
          ngModel.$ngfModelChange = false;
        }
      });
    }
  };

  function markModelAsDirty(ngModel, files) {
    if (files != null && !ngModel.$dirty) {
      if (ngModel.$setDirty) {
        ngModel.$setDirty();
      } else {
        ngModel.$dirty = true;
      }
    }
  }

  upload.applyModelValidation = function (ngModel, files) {
    markModelAsDirty(ngModel, files);
    angular.forEach(ngModel.$ngfValidations, function (validation) {
      ngModel.$setValidity(validation.name, validation.valid);
    });
  };

  upload.validate = function (files, ngModel, attr, scope) {
    ngModel = ngModel || {};
    ngModel.$ngfValidations = ngModel.$ngfValidations || [];

    angular.forEach(ngModel.$ngfValidations, function (v) {
      v.valid = true;
    });

    var attrGetter = function (name, params) {
      return upload.attrGetter(name, attr, scope, params);
    };

    if (files == null || files.length === 0) {
      return upload.emptyPromise(ngModel);
    }

    files = files.length === undefined ? [files] : files.slice(0);

    function validateSync(name, validatorVal, fn) {
      if (files) {
        var dName = 'ngf' + name[0].toUpperCase() + name.substr(1);
        var i = files.length, valid = null;
        while (i--) {
          var file = files[i];
          if (file) {
            var val = attrGetter(dName, {'$file': file});
            if (val == null) {
              val = validatorVal(attrGetter('ngfValidate') || {});
              valid = valid == null ? true : valid;
            }
            if (val != null) {
              if (!fn(file, val)) {
                file.$error = name;
                file.$errorParam = val;
                files.splice(i, 1);
                valid = false;
              }
            }
          }
        }
        if (valid !== null) {
          ngModel.$ngfValidations.push({name: name, valid: valid});
        }
      }
    }

    validateSync('pattern', function (cons) {
      return cons.pattern;
    }, upload.validatePattern);
    validateSync('minSize', function (cons) {
      return cons.size && cons.size.min;
    }, function (file, val) {
      return file.size >= upload.translateScalars(val);
    });
    validateSync('maxSize', function (cons) {
      return cons.size && cons.size.max;
    }, function (file, val) {
      return file.size <= upload.translateScalars(val);
    });
    var totalSize = 0;
    validateSync('maxTotalSize', function (cons) {
      return cons.maxTotalSize && cons.maxTotalSize;
    }, function (file, val) {
      totalSize += file.size;
      if (totalSize > upload.translateScalars(val)) {
        files.splice(0, files.length);
        return false;
      }
      return true;
    });

    validateSync('validateFn', function () {
      return null;
    }, function (file, r) {
      return r === true || r === null || r === '';
    });

    if (!files.length) {
      return upload.emptyPromise(ngModel, ngModel.$ngfValidations);
    }

    function validateAsync(name, validatorVal, type, asyncFn, fn) {
      var promises = [upload.emptyPromise()];
      if (files) {
        var dName = 'ngf' + name[0].toUpperCase() + name.substr(1);
        files = files.length === undefined ? [files] : files;
        angular.forEach(files, function (file) {
          var defer = $q.defer();
          promises.push(defer.promise);
          if (type && (file.type == null || file.type.search(type) !== 0)) {
            defer.resolve();
            return;
          }
          var val = attrGetter(dName, {'$file': file}) || validatorVal(attrGetter('ngfValidate', {'$file': file}) || {});
          if (val) {
            asyncFn(file, val).then(function (d) {
              if (!fn(d, val)) {
                file.$error = name;
                file.$errorParam = val;
                defer.reject();
              } else {
                defer.resolve();
              }
            }, function () {
              if (attrGetter('ngfValidateForce', {'$file': file})) {
                file.$error = name;
                file.$errorParam = val;
                defer.reject();
              } else {
                defer.resolve();
              }
            });
          } else {
            defer.resolve();
          }
        });
        return $q.all(promises).then(function () {
          ngModel.$ngfValidations.push({name: name, valid: true});
        }, function () {
          ngModel.$ngfValidations.push({name: name, valid: false});
        });
      }
    }

    var deffer = $q.defer();
    var promises = [];

    promises.push(upload.happyPromise(validateAsync('maxHeight', function (cons) {
      return cons.height && cons.height.max;
    }, /image/, this.imageDimensions, function (d, val) {
      return d.height <= val;
    })));
    promises.push(upload.happyPromise(validateAsync('minHeight', function (cons) {
      return cons.height && cons.height.min;
    }, /image/, this.imageDimensions, function (d, val) {
      return d.height >= val;
    })));
    promises.push(upload.happyPromise(validateAsync('maxWidth', function (cons) {
      return cons.width && cons.width.max;
    }, /image/, this.imageDimensions, function (d, val) {
      return d.width <= val;
    })));
    promises.push(upload.happyPromise(validateAsync('minWidth', function (cons) {
      return cons.width && cons.width.min;
    }, /image/, this.imageDimensions, function (d, val) {
      return d.width >= val;
    })));
    promises.push(upload.happyPromise(validateAsync('ratio', function (cons) {
      return cons.ratio;
    }, /image/, this.imageDimensions, function (d, val) {
      var split = val.toString().split(','), valid = false;

      for (var i = 0; i < split.length; i++) {
        var r = split[i], xIndex = r.search(/x/i);
        if (xIndex > -1) {
          r = parseFloat(r.substring(0, xIndex)) / parseFloat(r.substring(xIndex + 1));
        } else {
          r = parseFloat(r);
        }
        if (Math.abs((d.width / d.height) - r) < 0.0001) {
          valid = true;
        }
      }
      return valid;
    })));
    promises.push(upload.happyPromise(validateAsync('maxDuration', function (cons) {
      return cons.duration && cons.duration.max;
    }, /audio|video/, this.mediaDuration, function (d, val) {
      return d <= upload.translateScalars(val);
    })));
    promises.push(upload.happyPromise(validateAsync('minDuration', function (cons) {
      return cons.duration && cons.duration.min;
    }, /audio|video/, this.mediaDuration, function (d, val) {
      return d >= upload.translateScalars(val);
    })));

    promises.push(upload.happyPromise(validateAsync('validateAsyncFn', function () {
      return null;
    }, null, function (file, val) {
      return val;
    }, function (r) {
      return r === true || r === null || r === '';
    })));

    return $q.all(promises).then(function () {
      deffer.resolve(ngModel, ngModel.$ngfValidations);
    });
  };

  upload.imageDimensions = function (file) {
    if (file.$ngfWidth && file.$ngfHeight) {
      var d = $q.defer();
      $timeout(function () {
        d.resolve({width: file.$ngfWidth, height: file.$ngfHeight});
      });
      return d.promise;
    }
    if (file.$ngfDimensionPromise) return file.$ngfDimensionPromise;

    var deferred = $q.defer();
    $timeout(function () {
      if (file.type.indexOf('image') !== 0) {
        deferred.reject('not image');
        return;
      }
      upload.dataUrl(file).then(function (dataUrl) {
        var img = angular.element('<img>').attr('src', dataUrl).css('visibility', 'hidden').css('position', 'fixed');

        function success() {
          var width = img[0].clientWidth;
          var height = img[0].clientHeight;
          img.remove();
          file.$ngfWidth = width;
          file.$ngfHeight = height;
          deferred.resolve({width: width, height: height});
        }

        function error() {
          img.remove();
          deferred.reject('load error');
        }

        img.on('load', success);
        img.on('error', error);
        var count = 0;

        function checkLoadError() {
          $timeout(function () {
            if (img[0].parentNode) {
              if (img[0].clientWidth) {
                success();
              } else if (count > 10) {
                error();
              } else {
                checkLoadError();
              }
            }
          }, 1000);
        }

        checkLoadError();

        angular.element(document.getElementsByTagName('body')[0]).append(img);
      }, function () {
        deferred.reject('load error');
      });
    });

    file.$ngfDimensionPromise = deferred.promise;
    file.$ngfDimensionPromise['finally'](function () {
      delete file.$ngfDimensionPromise;
    });
    return file.$ngfDimensionPromise;
  };

  upload.mediaDuration = function (file) {
    if (file.$ngfDuration) {
      var d = $q.defer();
      $timeout(function () {
        d.resolve(file.$ngfDuration);
      });
      return d.promise;
    }
    if (file.$ngfDurationPromise) return file.$ngfDurationPromise;

    var deferred = $q.defer();
    $timeout(function () {
      if (file.type.indexOf('audio') !== 0 && file.type.indexOf('video') !== 0) {
        deferred.reject('not media');
        return;
      }
      upload.dataUrl(file).then(function (dataUrl) {
        var el = angular.element(file.type.indexOf('audio') === 0 ? '<audio>' : '<video>')
          .attr('src', dataUrl).css('visibility', 'none').css('position', 'fixed');

        function success() {
          var duration = el[0].duration;
          file.$ngfDuration = duration;
          el.remove();
          deferred.resolve(duration);
        }

        function error() {
          el.remove();
          deferred.reject('load error');
        }

        el.on('loadedmetadata', success);
        el.on('error', error);
        var count = 0;

        function checkLoadError() {
          $timeout(function () {
            if (el[0].parentNode) {
              if (el[0].duration) {
                success();
              } else if (count > 10) {
                error();
              } else {
                checkLoadError();
              }
            }
          }, 1000);
        }

        checkLoadError();

        angular.element(document.body).append(el);
      }, function () {
        deferred.reject('load error');
      });
    });

    file.$ngfDurationPromise = deferred.promise;
    file.$ngfDurationPromise['finally'](function () {
      delete file.$ngfDurationPromise;
    });
    return file.$ngfDurationPromise;
  };
  return upload;
}
]);

ngFileUpload.service('UploadResize', ['UploadValidate', '$q', function (UploadValidate, $q) {
  var upload = UploadValidate;

  /**
   * Conserve aspect ratio of the original region. Useful when shrinking/enlarging
   * images to fit into a certain area.
   * Source:  http://stackoverflow.com/a/14731922
   *
   * @param {Number} srcWidth Source area width
   * @param {Number} srcHeight Source area height
   * @param {Number} maxWidth Nestable area maximum available width
   * @param {Number} maxHeight Nestable area maximum available height
   * @return {Object} { width, height }
   */
  var calculateAspectRatioFit = function (srcWidth, srcHeight, maxWidth, maxHeight) {
    var ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    return {width: srcWidth * ratio, height: srcHeight * ratio};
  };

  // Extracted from https://github.com/romelgomez/angular-firebase-image-upload/blob/master/app/scripts/fileUpload.js#L89
  var resize = function (imagen, width, height, quality, type) {
    var deferred = $q.defer();
    var canvasElement = document.createElement('canvas');
    var imageElement = document.createElement('img');

    imageElement.onload = function () {
      try {
        if (!width) {
          width = imageElement.width;
        }
        if (!height) {
          height = imageElement.height;
        }
        var dimensions = calculateAspectRatioFit(imageElement.width, imageElement.height, width, height);
        canvasElement.width = dimensions.width;
        canvasElement.height = dimensions.height;
        var context = canvasElement.getContext('2d');
        context.drawImage(imageElement, 0, 0, dimensions.width, dimensions.height);
        deferred.resolve(canvasElement.toDataURL(type || 'image/WebP', quality || 1.0));
      } catch (e) {
        deferred.reject(e);
      }
    };
    imageElement.onerror = function () {
      deferred.reject();
    };
    imageElement.src = imagen;
    return deferred.promise;
  };

  upload.dataUrltoBlob = function (dataurl, name) {
    var arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    var blob = new Blob([u8arr], {type: mime});
    blob.name = name;
    return blob;
  };

  upload.isResizeSupported = function () {
    var elem = document.createElement('canvas');
    return window.atob && elem.getContext && elem.getContext('2d');
  };

  if (upload.isResizeSupported()) {
    // add name getter to the blob constructor prototype
    Object.defineProperty(Blob.prototype, 'name', {
      get: function () {
        return this.$ngfName;
      },
      set: function (v) {
        this.$ngfName = v;
      },
      configurable: true
    });
  }

  upload.resize = function (file, width, height, quality, type) {
    if (file.type.indexOf('image') !== 0) return upload.emptyPromise(file);

    var deferred = $q.defer();
    upload.dataUrl(file, true).then(function (url) {
      resize(url, width, height, quality, type || file.type).then(function (dataUrl) {
        deferred.resolve(upload.dataUrltoBlob(dataUrl, file.name));
      }, function () {
        deferred.reject();
      });
    }, function () {
      deferred.reject();
    });
    return deferred.promise;
  };

  return upload;
}]);

(function () {
  ngFileUpload.directive('ngfDrop', ['$parse', '$timeout', '$location', 'Upload', '$http',
    function ($parse, $timeout, $location, Upload, $http) {
      return {
        restrict: 'AEC',
        require: '?ngModel',
        link: function (scope, elem, attr, ngModel) {
          linkDrop(scope, elem, attr, ngModel, $parse, $timeout, $location, Upload, $http);
        }
      };
    }]);

  ngFileUpload.directive('ngfNoFileDrop', function () {
    return function (scope, elem) {
      if (dropAvailable()) elem.css('display', 'none');
    };
  });

  ngFileUpload.directive('ngfDropAvailable', ['$parse', '$timeout', 'Upload', function ($parse, $timeout, Upload) {
    return function (scope, elem, attr) {
      if (dropAvailable()) {
        var model = $parse(Upload.attrGetter('ngfDropAvailable', attr));
        $timeout(function () {
          model(scope);
          if (model.assign) {
            model.assign(scope, true);
          }
        });
      }
    };
  }]);

  function linkDrop(scope, elem, attr, ngModel, $parse, $timeout, $location, upload, $http) {
    var available = dropAvailable();

    var attrGetter = function (name, scope, params) {
      return upload.attrGetter(name, attr, scope, params);
    };

    if (attrGetter('dropAvailable')) {
      $timeout(function () {
        if (scope[attrGetter('dropAvailable')]) {
          scope[attrGetter('dropAvailable')].value = available;
        } else {
          scope[attrGetter('dropAvailable')] = available;
        }
      });
    }
    if (!available) {
      if (attrGetter('ngfHideOnDropNotAvailable', scope) === true) {
        elem.css('display', 'none');
      }
      return;
    }

    function isDisabled() {
      return elem.attr('disabled') || attrGetter('ngfDropDisabled', scope);
    }

    if (attrGetter('ngfSelect') == null) {
      upload.registerModelChangeValidator(ngModel, attr, scope);
    }

    var leaveTimeout = null;
    var stopPropagation = $parse(attrGetter('ngfStopPropagation'));
    var dragOverDelay = 1;
    var actualDragOverClass;

    elem[0].addEventListener('dragover', function (evt) {
      if (isDisabled()) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
      // handling dragover events from the Chrome download bar
      if (navigator.userAgent.indexOf('Chrome') > -1) {
        var b = evt.dataTransfer.effectAllowed;
        evt.dataTransfer.dropEffect = ('move' === b || 'linkMove' === b) ? 'move' : 'copy';
      }
      $timeout.cancel(leaveTimeout);
      if (!actualDragOverClass) {
        actualDragOverClass = 'C';
        calculateDragOverClass(scope, attr, evt, function (clazz) {
          actualDragOverClass = clazz;
          elem.addClass(actualDragOverClass);
          attrGetter('ngfDrag', scope, {$isDragging: true, $class: actualDragOverClass, $event: evt});
        });
      }
    }, false);
    elem[0].addEventListener('dragenter', function (evt) {
      if (isDisabled()) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
    }, false);
    elem[0].addEventListener('dragleave', function (evt) {
      if (isDisabled()) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
      leaveTimeout = $timeout(function () {
        if (actualDragOverClass) elem.removeClass(actualDragOverClass);
        actualDragOverClass = null;
        attrGetter('ngfDrag', scope, {$isDragging: false, $event: evt});
      }, dragOverDelay || 100);
    }, false);
    elem[0].addEventListener('drop', function (evt) {
      if (isDisabled() || !upload.shouldUpdateOn('drop', attr, scope)) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
      if (actualDragOverClass) elem.removeClass(actualDragOverClass);
      actualDragOverClass = null;
      var html;
      try {
        html = (evt.dataTransfer && evt.dataTransfer.getData && evt.dataTransfer.getData('text/html'));
      } catch (e) {/* Fix IE11 that throw error calling getData */}
      if (html) {
        var url;
        html.replace(/<img .*src *=\"([^\"]*)\"/, function (m, src) {
          url = src;
        });
        if (url) {
          $http({url: url, method: 'get', responseType: 'arraybuffer'}).then(function (resp) {
            var arrayBufferView = new Uint8Array(resp.data);
            var type = resp.headers('content-type') || 'image/WebP';
            var blob = new Blob([arrayBufferView], {type: type});
            //var split = type.split('[/;]');
            //blob.name = url.substring(0, 150).replace(/\W+/g, '') + '.' + (split.length > 1 ? split[1] : 'jpg');
            upload.updateModel(ngModel, attr, scope, attrGetter('ngfChange') || attrGetter('ngfDrop'), [blob], evt);
          });
        }
      } else {
        extractFiles(evt, function (files) {
            upload.updateModel(ngModel, attr, scope, attrGetter('ngfChange') || attrGetter('ngfDrop'), files, evt);
          }, attrGetter('ngfAllowDir', scope) !== false,
          attrGetter('multiple') || attrGetter('ngfMultiple', scope));
      }
    }, false);
    elem[0].addEventListener('paste', function (evt) {
      if (isDisabled() || !upload.shouldUpdateOn('paste', attr, scope)) return;
      var files = [];
      var clipboard = evt.clipboardData || evt.originalEvent.clipboardData;
      if (clipboard && clipboard.items) {
        for (var k = 0; k < clipboard.items.length; k++) {
          if (clipboard.items[k].type.indexOf('image') !== -1) {
            files.push(clipboard.items[k].getAsFile());
          }
        }
        upload.updateModel(ngModel, attr, scope, attrGetter('ngfChange') || attrGetter('ngfDrop'), files, evt);
      }
    }, false);

    function calculateDragOverClass(scope, attr, evt, callback) {
      var obj = attrGetter('ngfDragOverClass', scope, {$event: evt}), dClass = 'dragover';
      if (angular.isString(obj)) {
        dClass = obj;
      } else if (obj) {
        if (obj.delay) dragOverDelay = obj.delay;
        if (obj.accept || obj.reject) {
          var items = evt.dataTransfer.items;
          if (items == null || !items.length) {
            dClass = obj.accept;
          } else {
            var pattern = obj.pattern || attrGetter('ngfPattern', scope, {$event: evt});
            var len = items.length;
            while (len--) {
              if (!upload.validatePattern(items[len], pattern)) {
                dClass = obj.reject;
                break;
              } else {
                dClass = obj.accept;
              }
            }
          }
        }
      }
      callback(dClass);
    }

    function extractFiles(evt, callback, allowDir, multiple) {
      var files = [], processing = 0;

      function traverseFileTree(files, entry, path) {
        if (entry != null) {
          if (entry.isDirectory) {
            var filePath = (path || '') + entry.name;
            files.push({name: entry.name, type: 'directory', path: filePath});
            var dirReader = entry.createReader();
            var entries = [];
            processing++;
            var readEntries = function () {
              dirReader.readEntries(function (results) {
                try {
                  if (!results.length) {
                    for (var i = 0; i < entries.length; i++) {
                      traverseFileTree(files, entries[i], (path ? path : '') + entry.name + '/');
                    }
                    processing--;
                  } else {
                    entries = entries.concat(Array.prototype.slice.call(results || [], 0));
                    readEntries();
                  }
                } catch (e) {
                  processing--;
                  console.error(e);
                }
              }, function () {
                processing--;
              });
            };
            readEntries();
          } else {
            processing++;
            entry.file(function (file) {
              try {
                processing--;
                file.path = (path ? path : '') + file.name;
                files.push(file);
              } catch (e) {
                processing--;
                console.error(e);
              }
            }, function () {
              processing--;
            });
          }
        }
      }

      var items = evt.dataTransfer.items;

      if (items && items.length > 0 && $location.protocol() !== 'file') {
        for (var i = 0; i < items.length; i++) {
          if (items[i].webkitGetAsEntry && items[i].webkitGetAsEntry() && items[i].webkitGetAsEntry().isDirectory) {
            var entry = items[i].webkitGetAsEntry();
            if (entry.isDirectory && !allowDir) {
              continue;
            }
            if (entry != null) {
              traverseFileTree(files, entry);
            }
          } else {
            var f = items[i].getAsFile();
            if (f != null) files.push(f);
          }
          if (!multiple && files.length > 0) break;
        }
      } else {
        var fileList = evt.dataTransfer.files;
        if (fileList != null) {
          for (var j = 0; j < fileList.length; j++) {
            files.push(fileList.item(j));
            if (!multiple && files.length > 0) {
              break;
            }
          }
        }
      }
      var delays = 0;
      (function waitForProcess(delay) {
        $timeout(function () {
          if (!processing) {
            if (!multiple && files.length > 1) {
              i = 0;
              while (files[i].type === 'directory') i++;
              files = [files[i]];
            }
            callback(files);
          } else {
            if (delays++ * 10 < 20 * 1000) {
              waitForProcess(10);
            }
          }
        }, delay || 0);
      })();
    }
  }

  function dropAvailable() {
    var div = document.createElement('div');
    return ('draggable' in div) && ('ondrop' in div) && !/Edge\/12./i.test(navigator.userAgent);
  }

})();

// customized version of https://github.com/exif-js/exif-js
ngFileUpload.service('UploadExif', ['UploadResize', '$q', function (UploadResize, $q) {
  var upload = UploadResize;

  function findEXIFinJPEG(file) {
    var dataView = new DataView(file);

    if ((dataView.getUint8(0) !== 0xFF) || (dataView.getUint8(1) !== 0xD8)) {
      return 'Not a valid JPEG';
    }

    var offset = 2,
      length = file.byteLength,
      marker;

    while (offset < length) {
      if (dataView.getUint8(offset) !== 0xFF) {
        return 'Not a valid marker at offset ' + offset + ', found: ' + dataView.getUint8(offset);
      }

      marker = dataView.getUint8(offset + 1);
      if (marker === 225) {
        return readEXIFData(dataView, offset + 4, dataView.getUint16(offset + 2) - 2);
      } else {
        offset += 2 + dataView.getUint16(offset + 2);
      }
    }
  }

  function readOrientation(file, tiffStart, dirStart, bigEnd) {
    var entries = file.getUint16(dirStart, !bigEnd),
      entryOffset, i;

    for (i = 0; i < entries; i++) {
      entryOffset = dirStart + i * 12 + 2;
      var val = file.getUint16(entryOffset, !bigEnd);
      if (0x0112 === val) {
        return readTagValue(file, entryOffset, tiffStart, bigEnd);
      }
    }
    return null;
  }

  function readTagValue(file, entryOffset, tiffStart, bigEnd) {
    var numValues = file.getUint32(entryOffset + 4, !bigEnd),
      valueOffset = file.getUint32(entryOffset + 8, !bigEnd) + tiffStart, offset, vals, n;

    if (numValues === 1) {
      return file.getUint16(entryOffset + 8, !bigEnd);
    } else {
      offset = numValues > 2 ? valueOffset : (entryOffset + 8);
      vals = [];
      for (n = 0; n < numValues; n++) {
        vals[n] = file.getUint16(offset + 2 * n, !bigEnd);
      }
      return vals;
    }
  }

  function getStringFromDB(buffer, start, length) {
    var outstr = '';
    for (var n = start; n < start + length; n++) {
      outstr += String.fromCharCode(buffer.getUint8(n));
    }
    return outstr;
  }

  function readEXIFData(file, start) {
    if (getStringFromDB(file, start, 4) !== 'Exif') {
      return 'Not valid EXIF data! ' + getStringFromDB(file, start, 4);
    }

    var bigEnd,
      tiffOffset = start + 6;

    // test for TIFF validity and endianness
    if (file.getUint16(tiffOffset) === 0x4949) {
      bigEnd = false;
    } else if (file.getUint16(tiffOffset) === 0x4D4D) {
      bigEnd = true;
    } else {
      return 'Not valid TIFF data! (no 0x4949 or 0x4D4D)';
    }

    if (file.getUint16(tiffOffset + 2, !bigEnd) !== 0x002A) {
      return 'Not valid TIFF data! (no 0x002A)';
    }

    var firstIFDOffset = file.getUint32(tiffOffset + 4, !bigEnd);

    if (firstIFDOffset < 0x00000008) {
      return 'Not valid TIFF data! (First offset less than 8)', file.getUint32(tiffOffset + 4, !bigEnd);
    }

    return readOrientation(file, tiffOffset, tiffOffset + firstIFDOffset, bigEnd);

  }

  upload.isExifSupported = function() {
    return window.FileReader && new FileReader().readAsArrayBuffer && upload.isResizeSupported();
  };

  upload.orientation = function (file) {
    if (file.$ngfOrientation != null) {
      return upload.emptyPromise(file.$ngfOrientation);
    }
    var defer = $q.defer();
    var fileReader = new FileReader();
    fileReader.onload = function (e) {
      var orientation;
      try {
        orientation = findEXIFinJPEG(e.target.result);
      } catch (e) {
        defer.reject(e);
        return;
      }
      if (angular.isString(orientation)) {
        defer.reject(orientation);
      } else {
        file.$ngfOrientation = orientation;
        defer.resolve(orientation);
      }
    };
    fileReader.onerror = function (e) {
      defer.reject(e);
    };

    fileReader.readAsArrayBuffer(file);
    return defer.promise;
  };


  function applyTransform(ctx, orientation, width, height) {
    switch (orientation) {
      case 2:
        return ctx.transform(-1, 0, 0, 1, width, 0);
      case 3:
        return ctx.transform(-1, 0, 0, -1, width, height);
      case 4:
        return ctx.transform(1, 0, 0, -1, 0, height);
      case 5:
        return ctx.transform(0, 1, 1, 0, 0, 0);
      case 6:
        return ctx.transform(0, 1, -1, 0, height, 0);
      case 7:
        return ctx.transform(0, -1, -1, 0, height, width);
      case 8:
        return ctx.transform(0, -1, 1, 0, 0, width);
    }
  }

  upload.applyExifRotation = function (file) {
    if (file.type.indexOf('image/jpeg') !== 0) {
      return upload.emptyPromise(file);
    }

    var deferred = $q.defer();
    upload.orientation(file).then(function (orientation) {
      if (!orientation || orientation < 2 || orientation > 8) {
        deferred.resolve(file);
      }
      upload.dataUrl(file, true).then(function (url) {
        var canvas = document.createElement('canvas');
        var img = document.createElement('img');

        img.onload = function () {
          try {
            canvas.width = orientation > 4 ? img.height : img.width;
            canvas.height = orientation > 4 ? img.width : img.height;
            var ctx = canvas.getContext('2d');
            applyTransform(ctx, orientation, img.width, img.height);
            ctx.drawImage(img, 0, 0);
            var dataUrl = canvas.toDataURL(file.type || 'image/WebP', 1.0);
            var blob = upload.dataUrltoBlob(dataUrl, file.name);
            deferred.resolve(blob);
          } catch (e) {
            deferred.reject(e);
          }
        };
        img.onerror = function () {
          deferred.reject();
        };
        img.src = url;
      }, function (e) {
        deferred.reject(e);
      });
    }, function (e) {
      deferred.reject(e);
    });
    return deferred.promise;
  };

  return upload;
}]);


/* ng-infinite-scroll - v1.2.0 - 2015-02-14 */
var mod;

mod = angular.module('infinite-scroll', []);

mod.value('THROTTLE_MILLISECONDS', null);

mod.directive('infiniteScroll', [
  '$rootScope', '$window', '$interval', 'THROTTLE_MILLISECONDS', function($rootScope, $window, $interval, THROTTLE_MILLISECONDS) {
    return {
      scope: {
        infiniteScroll: '&',
        infiniteScrollContainer: '=',
        infiniteScrollDistance: '=',
        infiniteScrollDisabled: '=',
        infiniteScrollUseDocumentBottom: '=',
        infiniteScrollListenForEvent: '@'
      },
      link: function(scope, elem, attrs) {
        var changeContainer, checkWhenEnabled, container, handleInfiniteScrollContainer, handleInfiniteScrollDisabled, handleInfiniteScrollDistance, handleInfiniteScrollUseDocumentBottom, handler, height, immediateCheck, offsetTop, pageYOffset, scrollDistance, scrollEnabled, throttle, unregisterEventListener, useDocumentBottom, windowElement;
        windowElement = angular.element($window);
        scrollDistance = null;
        scrollEnabled = null;
        checkWhenEnabled = null;
        container = null;
        immediateCheck = true;
        useDocumentBottom = false;
        unregisterEventListener = null;
        height = function(elem) {
          elem = elem[0] || elem;
          if (isNaN(elem.offsetHeight)) {
            return elem.document.documentElement.clientHeight;
          } else {
            return elem.offsetHeight;
          }
        };
        offsetTop = function(elem) {
          if (!elem[0].getBoundingClientRect || elem.css('none')) {
            return;
          }
          return elem[0].getBoundingClientRect().top + pageYOffset(elem);
        };
        pageYOffset = function(elem) {
          elem = elem[0] || elem;
          if (isNaN(window.pageYOffset)) {
            return elem.document.documentElement.scrollTop;
          } else {
            return elem.ownerDocument.defaultView.pageYOffset;
          }
        };
        handler = function() {
          var containerBottom, containerTopOffset, elementBottom, remaining, shouldScroll;
          if (container === windowElement) {
            containerBottom = height(container) + pageYOffset(container[0].document.documentElement);
            elementBottom = offsetTop(elem) + height(elem);
          } else {
            containerBottom = height(container);
            containerTopOffset = 0;
            if (offsetTop(container) !== void 0) {
              containerTopOffset = offsetTop(container);
            }
            elementBottom = offsetTop(elem) - containerTopOffset + height(elem);
          }
          if (useDocumentBottom) {
            elementBottom = height((elem[0].ownerDocument || elem[0].document).documentElement);
          }
          remaining = elementBottom - containerBottom;
          shouldScroll = remaining <= height(container) * scrollDistance + 1;
          if (shouldScroll) {
            checkWhenEnabled = true;
            if (scrollEnabled) {
              if (scope.$$phase || $rootScope.$$phase) {
                return scope.infiniteScroll();
              } else {
                return scope.$apply(scope.infiniteScroll);
              }
            }
          } else {
            return checkWhenEnabled = false;
          }
        };
        throttle = function(func, wait) {
          var later, previous, timeout;
          timeout = null;
          previous = 0;
          later = function() {
            var context;
            previous = new Date().getTime();
            $interval.cancel(timeout);
            timeout = null;
            func.call();
            return context = null;
          };
          return function() {
            var now, remaining;
            now = new Date().getTime();
            remaining = wait - (now - previous);
            if (remaining <= 0) {
              clearTimeout(timeout);
              $interval.cancel(timeout);
              timeout = null;
              previous = now;
              return func.call();
            } else {
              if (!timeout) {
                return timeout = $interval(later, remaining, 1);
              }
            }
          };
        };
        if (THROTTLE_MILLISECONDS != null) {
          handler = throttle(handler, THROTTLE_MILLISECONDS);
        }
        scope.$on('$destroy', function() {
          container.unbind('scroll', handler);
          if (unregisterEventListener != null) {
            unregisterEventListener();
            return unregisterEventListener = null;
          }
        });
        handleInfiniteScrollDistance = function(v) {
          return scrollDistance = parseFloat(v) || 0;
        };
        scope.$watch('infiniteScrollDistance', handleInfiniteScrollDistance);
        handleInfiniteScrollDistance(scope.infiniteScrollDistance);
        handleInfiniteScrollDisabled = function(v) {
          scrollEnabled = !v;
          if (scrollEnabled && checkWhenEnabled) {
            checkWhenEnabled = false;
            return handler();
          }
        };
        scope.$watch('infiniteScrollDisabled', handleInfiniteScrollDisabled);
        handleInfiniteScrollDisabled(scope.infiniteScrollDisabled);
        handleInfiniteScrollUseDocumentBottom = function(v) {
          return useDocumentBottom = v;
        };
        scope.$watch('infiniteScrollUseDocumentBottom', handleInfiniteScrollUseDocumentBottom);
        handleInfiniteScrollUseDocumentBottom(scope.infiniteScrollUseDocumentBottom);
        changeContainer = function(newContainer) {
          if (container != null) {
            container.unbind('scroll', handler);
          }
          container = newContainer;
          if (newContainer != null) {
            return container.bind('scroll', handler);
          }
        };
        changeContainer(windowElement);
        if (scope.infiniteScrollListenForEvent) {
          unregisterEventListener = $rootScope.$on(scope.infiniteScrollListenForEvent, handler);
        }
        handleInfiniteScrollContainer = function(newContainer) {
          if ((newContainer == null) || newContainer.length === 0) {
            return;
          }
          if (newContainer instanceof HTMLElement) {
            newContainer = angular.element(newContainer);
          } else if (typeof newContainer.append === 'function') {
            newContainer = angular.element(newContainer[newContainer.length - 1]);
          } else if (typeof newContainer === 'string') {
            newContainer = angular.element(document.querySelector(newContainer));
          }
          if (newContainer != null) {
            return changeContainer(newContainer);
          } else {
            throw new Exception("invalid infinite-scroll-container attribute.");
          }
        };
        scope.$watch('infiniteScrollContainer', handleInfiniteScrollContainer);
        handleInfiniteScrollContainer(scope.infiniteScrollContainer || []);
        if (attrs.infiniteScrollParent != null) {
          changeContainer(angular.element(elem.parent()));
        }
        if (attrs.infiniteScrollImmediateCheck != null) {
          immediateCheck = scope.$eval(attrs.infiniteScrollImmediateCheck);
        }
        return $interval((function() {
          if (immediateCheck) {
            return handler();
          }
        }), 0, 1);
      }
    };
  }
]);

// OpenLayers 3. See http://openlayers.org/
// License: https://raw.githubusercontent.com/openlayers/ol3/master/LICENSE.md
// Version: v3.18.2
;(function (root, factory) {
  if (typeof exports === "object") {
    module.exports = factory();
  } else if (typeof define === "function" && define.amd) {
    define([], factory);
  } else {
    root.ol = factory();
  }
}(this, function () {
  var OPENLAYERS = {};
  var k,aa=this;
function ba(){var a=aa.setImmediate,b=typeof a;if("object"==b)if(a){if(a instanceof Array)return"array";if(a instanceof Object)return b;var c=Object.prototype.toString.call(a);if("[object Window]"==c)return"object";if("[object Array]"==c||"number"==typeof a.length&&"undefined"!=typeof a.splice&&"undefined"!=typeof a.propertyIsEnumerable&&!a.propertyIsEnumerable("splice"))return"array";if("[object Function]"==c||"undefined"!=typeof a.call&&"undefined"!=typeof a.propertyIsEnumerable&&!a.propertyIsEnumerable("call"))return"function"}else return"null";
else if("function"==b&&"undefined"==typeof a.call)return"object";return b}function ca(a,b,c){return a.call.apply(a.bind,arguments)}function da(a,b,c){if(!a)throw Error();if(2<arguments.length){var d=Array.prototype.slice.call(arguments,2);return function(){var c=Array.prototype.slice.call(arguments);Array.prototype.unshift.apply(c,d);return a.apply(b,c)}}return function(){return a.apply(b,arguments)}}
function ea(a,b,c){ea=Function.prototype.bind&&-1!=Function.prototype.bind.toString().indexOf("native code")?ca:da;return ea.apply(null,arguments)}function r(a,b){var c=a.split("."),d=OPENLAYERS||aa;c[0]in d||!d.execScript||d.execScript("var "+c[0]);for(var e;c.length&&(e=c.shift());)c.length||void 0===b?d[e]?d=d[e]:d=d[e]={}:d[e]=b};var fa,ga;function w(a,b){a.prototype=Object.create(b.prototype);a.prototype.constructor=a}function ha(){}function x(a){return a.Wn||(a.Wn=++ia)}var ia=0;if("undefined"!==typeof window)var ja=window;else"undefined"!==typeof global?ja=global:"undefined"!==typeof self&&(ja=self);function ka(a){this.message="Assertion failed. See http://openlayers.org/en/v3.18.2/doc/errors/#"+a+" for details.";this.code=a;this.name="AssertionError"}w(ka,Error);function la(a,b){if(!a)throw new ka(b);};function ma(a,b,c){return Math.min(Math.max(a,b),c)}var na=function(){var a;"cosh"in Math?a=Math.cosh:a=function(a){a=Math.exp(a);return(a+1/a)/2};return a}();function pa(a){la(0<a,29);return Math.pow(2,Math.ceil(Math.log(a)/Math.LN2))}function qa(a,b,c,d,e,f){var g=e-c,h=f-d;if(0!==g||0!==h){var l=((a-c)*g+(b-d)*h)/(g*g+h*h);1<l?(c=e,d=f):0<l&&(c+=g*l,d+=h*l)}return ra(a,b,c,d)}function ra(a,b,c,d){a=c-a;b=d-b;return a*a+b*b}function sa(a){return a*Math.PI/180}
function ta(a,b){var c=a%b;return 0>c*b?c+b:c}function va(a,b,c){return a+c*(b-a)};function wa(a){return function(b){if(b)return[ma(b[0],a[0],a[2]),ma(b[1],a[1],a[3])]}}function xa(a){return a};function ya(a,b,c){this.center=a;this.resolution=b;this.rotation=c};var za="function"===typeof Object.assign?Object.assign:function(a,b){if(!a||!a)throw new TypeError("Cannot convert undefined or null to object");for(var c=Object(a),d=1,e=arguments.length;d<e;++d){var f=arguments[d];if(void 0!==f&&null!==f)for(var g in f)f.hasOwnProperty(g)&&(c[g]=f[g])}return c};function Aa(a){for(var b in a)delete a[b]}function Ba(a){var b=[],c;for(c in a)b.push(a[c]);return b}function Ca(a){for(var b in a)return!1;return!b};function Da(a){function b(b){var d=a.listener,e=a.mg||a.target;a.og&&Ea(a);return d.call(e,b)}return a.ng=b}function Fa(a,b,c,d){for(var e,f=0,g=a.length;f<g;++f)if(e=a[f],e.listener===b&&e.mg===c)return d&&(e.deleteIndex=f),e}function Ga(a,b){var c=a.cb;return c?c[b]:void 0}function Ha(a){var b=a.cb;b||(b=a.cb={});return b}
function Ia(a,b){var c=Ga(a,b);if(c){for(var d=0,e=c.length;d<e;++d)a.removeEventListener(b,c[d].ng),Aa(c[d]);c.length=0;if(c=a.cb)delete c[b],0===Object.keys(c).length&&delete a.cb}}function B(a,b,c,d,e){var f=Ha(a),g=f[b];g||(g=f[b]=[]);(f=Fa(g,c,d,!1))?e||(f.og=!1):(f={mg:d,og:!!e,listener:c,target:a,type:b},a.addEventListener(b,Da(f)),g.push(f));return f}function Ja(a,b,c,d){return B(a,b,c,d,!0)}function Ka(a,b,c,d){(a=Ga(a,b))&&(c=Fa(a,c,d,!0))&&Ea(c)}
function Ea(a){if(a&&a.target){a.target.removeEventListener(a.type,a.ng);var b=Ga(a.target,a.type);if(b){var c="deleteIndex"in a?a.deleteIndex:b.indexOf(a);-1!==c&&b.splice(c,1);0===b.length&&Ia(a.target,a.type)}Aa(a)}}function La(a){var b=Ha(a),c;for(c in b)Ia(a,c)};function Na(){}Na.prototype.Lb=!1;function Oa(a){a.Lb||(a.Lb=!0,a.ma())}Na.prototype.ma=ha;function Pa(a){this.type=a;this.target=null}Pa.prototype.preventDefault=Pa.prototype.stopPropagation=function(){this.qo=!0};function Qa(a){a.stopPropagation()}function Ra(a){a.preventDefault()};function Sa(){this.Va={};this.Ba={};this.Aa={}}w(Sa,Na);Sa.prototype.addEventListener=function(a,b){var c=this.Aa[a];c||(c=this.Aa[a]=[]);-1===c.indexOf(b)&&c.push(b)};
Sa.prototype.b=function(a){var b="string"===typeof a?new Pa(a):a;a=b.type;b.target=this;var c=this.Aa[a],d;if(c){a in this.Ba||(this.Ba[a]=0,this.Va[a]=0);++this.Ba[a];for(var e=0,f=c.length;e<f;++e)if(!1===c[e].call(this,b)||b.qo){d=!1;break}--this.Ba[a];if(0===this.Ba[a]){b=this.Va[a];for(delete this.Va[a];b--;)this.removeEventListener(a,ha);delete this.Ba[a]}return d}};Sa.prototype.ma=function(){La(this)};function Ta(a,b){return b?b in a.Aa:0<Object.keys(a.Aa).length}
Sa.prototype.removeEventListener=function(a,b){var c=this.Aa[a];if(c){var d=c.indexOf(b);a in this.Va?(c[d]=ha,++this.Va[a]):(c.splice(d,1),0===c.length&&delete this.Aa[a])}};function Ua(){Sa.call(this);this.g=0}w(Ua,Sa);function Va(a){if(Array.isArray(a))for(var b=0,c=a.length;b<c;++b)Ea(a[b]);else Ea(a)}k=Ua.prototype;k.u=function(){++this.g;this.b("change")};k.K=function(){return this.g};k.I=function(a,b,c){if(Array.isArray(a)){for(var d=a.length,e=Array(d),f=0;f<d;++f)e[f]=B(this,a[f],b,c);return e}return B(this,a,b,c)};k.L=function(a,b,c){if(Array.isArray(a)){for(var d=a.length,e=Array(d),f=0;f<d;++f)e[f]=Ja(this,a[f],b,c);return e}return Ja(this,a,b,c)};
k.J=function(a,b,c){if(Array.isArray(a))for(var d=0,e=a.length;d<e;++d)Ka(this,a[d],b,c);else Ka(this,a,b,c)};k.M=Va;function Wa(a,b,c){Pa.call(this,a);this.key=b;this.oldValue=c}w(Wa,Pa);function Xa(a){Ua.call(this);x(this);this.U={};void 0!==a&&this.G(a)}w(Xa,Ua);var Ya={};function Za(a){return Ya.hasOwnProperty(a)?Ya[a]:Ya[a]="change:"+a}k=Xa.prototype;k.get=function(a){var b;this.U.hasOwnProperty(a)&&(b=this.U[a]);return b};k.P=function(){return Object.keys(this.U)};k.N=function(){return za({},this.U)};function $a(a,b,c){var d;d=Za(b);a.b(new Wa(d,b,c));a.b(new Wa("propertychange",b,c))}
k.set=function(a,b,c){c?this.U[a]=b:(c=this.U[a],this.U[a]=b,c!==b&&$a(this,a,c))};k.G=function(a,b){for(var c in a)this.set(c,a[c],b)};k.S=function(a,b){if(a in this.U){var c=this.U[a];delete this.U[a];b||$a(this,a,c)}};function ab(a,b){return a>b?1:a<b?-1:0}function bb(a,b){return 0<=a.indexOf(b)}function cb(a,b,c){var d=a.length;if(a[0]<=b)return 0;if(!(b<=a[d-1]))if(0<c)for(c=1;c<d;++c){if(a[c]<b)return c-1}else if(0>c)for(c=1;c<d;++c){if(a[c]<=b)return c}else for(c=1;c<d;++c){if(a[c]==b)return c;if(a[c]<b)return a[c-1]-b<b-a[c]?c-1:c}return d-1}function db(a){return a.reduce(function(a,c){return Array.isArray(c)?a.concat(db(c)):a.concat(c)},[])}
function eb(a,b){var c,d=Array.isArray(b)?b:[b],e=d.length;for(c=0;c<e;c++)a[a.length]=d[c]}function fb(a,b){var c=a.indexOf(b),d=-1<c;d&&a.splice(c,1);return d}function gb(a,b){for(var c=a.length>>>0,d,e=0;e<c;e++)if(d=a[e],b(d,e,a))return d;return null}function hb(a,b){var c=a.length;if(c!==b.length)return!1;for(var d=0;d<c;d++)if(a[d]!==b[d])return!1;return!0}
function ib(a){var b=jb,c=a.length,d=Array(a.length),e;for(e=0;e<c;e++)d[e]={index:e,value:a[e]};d.sort(function(a,c){return b(a.value,c.value)||a.index-c.index});for(e=0;e<a.length;e++)a[e]=d[e].value}function kb(a,b){var c;return a.every(function(d,e){c=e;return!b(d,e,a)})?-1:c}function lb(a,b){var c=b||ab;return a.every(function(b,e){if(0===e)return!0;var f=c(a[e-1],b);return!(0<f||0===f)})};function mb(a){return function(b,c,d){if(void 0!==b)return b=cb(a,b,d),b=ma(b+c,0,a.length-1),c=Math.floor(b),b!=c&&c<a.length-1?a[c]/Math.pow(a[c]/a[c+1],b-c):a[c]}}function nb(a,b,c){return function(d,e,f){if(void 0!==d)return d=Math.max(Math.floor(Math.log(b/d)/Math.log(a)+(-f/2+.5))+e,0),void 0!==c&&(d=Math.min(d,c)),b/Math.pow(a,d)}};function ob(a){if(void 0!==a)return 0}function pb(a,b){if(void 0!==a)return a+b}function qb(a){var b=2*Math.PI/a;return function(a,d){if(void 0!==a)return a=Math.floor((a+d)/b+.5)*b}}function rb(){var a=sa(5);return function(b,c){if(void 0!==b)return Math.abs(b+c)<=a?0:b+c}};function sb(a,b){var c=void 0!==b?a.toFixed(b):""+a,d=c.indexOf("."),d=-1===d?c.length:d;return 2<d?c:Array(3-d).join("0")+c}function tb(a){a=(""+a).split(".");for(var b=["1","3"],c=0;c<Math.max(a.length,b.length);c++){var d=parseInt(a[c]||"0",10),e=parseInt(b[c]||"0",10);if(d>e)return 1;if(e>d)return-1}return 0};function ub(a,b){a[0]+=b[0];a[1]+=b[1];return a}function vb(a,b){var c=a[0],d=a[1],e=b[0],f=b[1],g=e[0],e=e[1],h=f[0],f=f[1],l=h-g,m=f-e,c=0===l&&0===m?0:(l*(c-g)+m*(d-e))/(l*l+m*m||0);0>=c||(1<=c?(g=h,e=f):(g+=c*l,e+=c*m));return[g,e]}function wb(a,b,c){a=ta(a+180,360)-180;var d=Math.abs(3600*a);return Math.floor(d/3600)+"\u00b0 "+sb(Math.floor(d/60%60))+"\u2032 "+sb(d%60,c||0)+"\u2033 "+b.charAt(0>a?1:0)}
function xb(a,b,c){return a?b.replace("{x}",a[0].toFixed(c)).replace("{y}",a[1].toFixed(c)):""}function yb(a,b){for(var c=!0,d=a.length-1;0<=d;--d)if(a[d]!=b[d]){c=!1;break}return c}function zb(a,b){var c=Math.cos(b),d=Math.sin(b),e=a[1]*c+a[0]*d;a[0]=a[0]*c-a[1]*d;a[1]=e;return a}function Ab(a,b){var c=a[0]-b[0],d=a[1]-b[1];return c*c+d*d}function Bb(a,b){return Ab(a,vb(a,b))}function Cb(a,b){return xb(a,"{x}, {y}",b)};function Db(a){for(var b=Eb(),c=0,d=a.length;c<d;++c)Fb(b,a[c]);return b}function Gb(a,b,c){return c?(c[0]=a[0]-b,c[1]=a[1]-b,c[2]=a[2]+b,c[3]=a[3]+b,c):[a[0]-b,a[1]-b,a[2]+b,a[3]+b]}function Hb(a,b){return b?(b[0]=a[0],b[1]=a[1],b[2]=a[2],b[3]=a[3],b):a.slice()}function Kb(a,b,c){b=b<a[0]?a[0]-b:a[2]<b?b-a[2]:0;a=c<a[1]?a[1]-c:a[3]<c?c-a[3]:0;return b*b+a*a}function Lb(a,b){return Mb(a,b[0],b[1])}function Nb(a,b){return a[0]<=b[0]&&b[2]<=a[2]&&a[1]<=b[1]&&b[3]<=a[3]}
function Mb(a,b,c){return a[0]<=b&&b<=a[2]&&a[1]<=c&&c<=a[3]}function Ob(a,b){var c=a[1],d=a[2],e=a[3],f=b[0],g=b[1],h=0;f<a[0]?h|=16:f>d&&(h|=4);g<c?h|=8:g>e&&(h|=2);0===h&&(h=1);return h}function Eb(){return[Infinity,Infinity,-Infinity,-Infinity]}function Qb(a,b,c,d,e){return e?(e[0]=a,e[1]=b,e[2]=c,e[3]=d,e):[a,b,c,d]}function Rb(a,b){var c=a[0],d=a[1];return Qb(c,d,c,d,b)}function Sb(a,b,c,d,e){e=Qb(Infinity,Infinity,-Infinity,-Infinity,e);return Tb(e,a,b,c,d)}
function Ub(a,b){return a[0]==b[0]&&a[2]==b[2]&&a[1]==b[1]&&a[3]==b[3]}function Vb(a,b){b[0]<a[0]&&(a[0]=b[0]);b[2]>a[2]&&(a[2]=b[2]);b[1]<a[1]&&(a[1]=b[1]);b[3]>a[3]&&(a[3]=b[3]);return a}function Fb(a,b){b[0]<a[0]&&(a[0]=b[0]);b[0]>a[2]&&(a[2]=b[0]);b[1]<a[1]&&(a[1]=b[1]);b[1]>a[3]&&(a[3]=b[1])}function Tb(a,b,c,d,e){for(;c<d;c+=e){var f=a,g=b[c],h=b[c+1];f[0]=Math.min(f[0],g);f[1]=Math.min(f[1],h);f[2]=Math.max(f[2],g);f[3]=Math.max(f[3],h)}return a}
function Xb(a,b,c){var d;return(d=b.call(c,Zb(a)))||(d=b.call(c,$b(a)))||(d=b.call(c,ac(a)))?d:(d=b.call(c,bc(a)))?d:!1}function cc(a){var b=0;dc(a)||(b=ec(a)*fc(a));return b}function Zb(a){return[a[0],a[1]]}function $b(a){return[a[2],a[1]]}function gc(a){return[(a[0]+a[2])/2,(a[1]+a[3])/2]}
function ic(a,b,c,d,e){var f=b*d[0]/2;d=b*d[1]/2;b=Math.cos(c);var g=Math.sin(c);c=f*b;f*=g;b*=d;var h=d*g,l=a[0],m=a[1];a=l-c+h;d=l-c-h;g=l+c-h;c=l+c+h;var h=m-f-b,l=m-f+b,n=m+f+b,f=m+f-b;return Qb(Math.min(a,d,g,c),Math.min(h,l,n,f),Math.max(a,d,g,c),Math.max(h,l,n,f),e)}function fc(a){return a[3]-a[1]}function jc(a,b,c){c=c?c:Eb();kc(a,b)&&(c[0]=a[0]>b[0]?a[0]:b[0],c[1]=a[1]>b[1]?a[1]:b[1],c[2]=a[2]<b[2]?a[2]:b[2],c[3]=a[3]<b[3]?a[3]:b[3]);return c}function bc(a){return[a[0],a[3]]}
function ac(a){return[a[2],a[3]]}function ec(a){return a[2]-a[0]}function kc(a,b){return a[0]<=b[2]&&a[2]>=b[0]&&a[1]<=b[3]&&a[3]>=b[1]}function dc(a){return a[2]<a[0]||a[3]<a[1]}function lc(a,b){var c=(a[2]-a[0])/2*(b-1),d=(a[3]-a[1])/2*(b-1);a[0]-=c;a[2]+=c;a[1]-=d;a[3]+=d}
function mc(a,b,c){a=[a[0],a[1],a[0],a[3],a[2],a[1],a[2],a[3]];b(a,a,2);var d=[a[0],a[2],a[4],a[6]],e=[a[1],a[3],a[5],a[7]];b=Math.min.apply(null,d);a=Math.min.apply(null,e);d=Math.max.apply(null,d);e=Math.max.apply(null,e);return Qb(b,a,d,e,c)};function nc(){return!0}function oc(){return!1};/*

 Latitude/longitude spherical geodesy formulae taken from
 http://www.movable-type.co.uk/scripts/latlong.html
 Licensed under CC-BY-3.0.
*/
function pc(a){this.radius=a}pc.prototype.a=function(a){for(var b=0,c=a.length,d=a[c-1][0],e=a[c-1][1],f=0;f<c;f++)var g=a[f][0],h=a[f][1],b=b+sa(g-d)*(2+Math.sin(sa(e))+Math.sin(sa(h))),d=g,e=h;return b*this.radius*this.radius/2};pc.prototype.b=function(a,b){var c=sa(a[1]),d=sa(b[1]),e=(d-c)/2,f=sa(b[0]-a[0])/2,c=Math.sin(e)*Math.sin(e)+Math.sin(f)*Math.sin(f)*Math.cos(c)*Math.cos(d);return 2*this.radius*Math.atan2(Math.sqrt(c),Math.sqrt(1-c))};
pc.prototype.offset=function(a,b,c){var d=sa(a[1]);b/=this.radius;var e=Math.asin(Math.sin(d)*Math.cos(b)+Math.cos(d)*Math.sin(b)*Math.cos(c));return[180*(sa(a[0])+Math.atan2(Math.sin(c)*Math.sin(b)*Math.cos(d),Math.cos(b)-Math.sin(d)*Math.sin(e)))/Math.PI,180*e/Math.PI]};var qc=new pc(6370997);var rc={};rc.degrees=2*Math.PI*qc.radius/360;rc.ft=.3048;rc.m=1;rc["us-ft"]=1200/3937;
function sc(a){this.eb=a.code;this.c=a.units;this.f=void 0!==a.extent?a.extent:null;this.i=void 0!==a.worldExtent?a.worldExtent:null;this.b=void 0!==a.axisOrientation?a.axisOrientation:"enu";this.g=void 0!==a.global?a.global:!1;this.a=!(!this.g||!this.f);this.j=void 0!==a.getPointResolution?a.getPointResolution:this.pk;this.l=null;this.o=a.metersPerUnit;var b=tc,c=a.code,d=uc||ja.proj4;if("function"==typeof d&&void 0===b[c]){var e=d.defs(c);if(void 0!==e){void 0!==e.axis&&void 0===a.axisOrientation&&
(this.b=e.axis);void 0===a.metersPerUnit&&(this.o=e.to_meter);void 0===a.units&&(this.c=e.units);for(var f in b)b=d.defs(f),void 0!==b&&(a=vc(f),b===e?wc([a,this]):(b=d(f,c),xc(a,this,b.forward,b.inverse)))}}}k=sc.prototype;k.Pj=function(){return this.eb};k.C=function(){return this.f};k.Ab=function(){return this.c};k.cc=function(){return this.o||rc[this.c]};k.Bk=function(){return this.i};k.kl=function(){return this.g};k.ap=function(a){this.g=a;this.a=!(!a||!this.f)};
k.Hm=function(a){this.f=a;this.a=!(!this.g||!a)};k.jp=function(a){this.i=a};k.$o=function(a){this.j=a};k.pk=function(a,b){if("degrees"==this.Ab())return a;var c=yc(this,vc("EPSG:4326")),d=[b[0]-a/2,b[1],b[0]+a/2,b[1],b[0],b[1]-a/2,b[0],b[1]+a/2],d=c(d,d,2),c=qc.b(d.slice(0,2),d.slice(2,4)),d=qc.b(d.slice(4,6),d.slice(6,8)),d=(c+d)/2,c=this.cc();void 0!==c&&(d/=c);return d};k.getPointResolution=function(a,b){return this.j(a,b)};var tc={},zc={},uc=null;
function wc(a){Ac(a);a.forEach(function(b){a.forEach(function(a){b!==a&&Bc(b,a,Cc)})})}function Dc(){var a=Ec,b=Fc,c=Gc;Hc.forEach(function(d){a.forEach(function(a){Bc(d,a,b);Bc(a,d,c)})})}function Ic(a){tc[a.eb]=a;Bc(a,a,Cc)}function Ac(a){var b=[];a.forEach(function(a){b.push(Ic(a))})}function Jc(a){return a?"string"===typeof a?vc(a):a:vc("EPSG:3857")}function Bc(a,b,c){a=a.eb;b=b.eb;a in zc||(zc[a]={});zc[a][b]=c}function xc(a,b,c,d){a=vc(a);b=vc(b);Bc(a,b,Kc(c));Bc(b,a,Kc(d))}
function Kc(a){return function(b,c,d){var e=b.length;d=void 0!==d?d:2;c=void 0!==c?c:Array(e);var f,g;for(g=0;g<e;g+=d)for(f=a([b[g],b[g+1]]),c[g]=f[0],c[g+1]=f[1],f=d-1;2<=f;--f)c[g+f]=b[g+f];return c}}function vc(a){var b;if(a instanceof sc)b=a;else if("string"===typeof a){b=tc[a];var c=uc||ja.proj4;void 0===b&&"function"==typeof c&&void 0!==c.defs(a)&&(b=new sc({code:a}),Ic(b))}return b||null}function Lc(a,b){if(a===b)return!0;var c=a.Ab()===b.Ab();return a.eb===b.eb?c:yc(a,b)===Cc&&c}
function Mc(a,b){var c=vc(a),d=vc(b);return yc(c,d)}function yc(a,b){var c=a.eb,d=b.eb,e;c in zc&&d in zc[c]&&(e=zc[c][d]);void 0===e&&(e=Nc);return e}function Nc(a,b){if(void 0!==b&&a!==b){for(var c=0,d=a.length;c<d;++c)b[c]=a[c];a=b}return a}function Cc(a,b){var c;if(void 0!==b){c=0;for(var d=a.length;c<d;++c)b[c]=a[c];c=b}else c=a.slice();return c}function Oc(a,b,c){return Mc(b,c)(a,void 0,a.length)}function Pc(a,b,c){b=Mc(b,c);return mc(a,b)};function Qc(){Xa.call(this);this.s=Eb();this.v=-1;this.i={};this.o=this.l=0}w(Qc,Xa);k=Qc.prototype;k.yb=function(a,b){var c=b?b:[NaN,NaN];this.vb(a[0],a[1],c,Infinity);return c};k.jb=function(a){return this.zc(a[0],a[1])};k.zc=oc;k.C=function(a){this.v!=this.g&&(this.s=this.Nd(this.s),this.v=this.g);var b=this.s;a?(a[0]=b[0],a[1]=b[1],a[2]=b[2],a[3]=b[3]):a=b;return a};k.Fb=function(a){return this.md(a*a)};k.lb=function(a,b){this.rc(Mc(a,b));return this};function Rc(a,b,c,d,e,f){for(var g=f?f:[],h=0;b<c;b+=d){var l=a[b],m=a[b+1];g[h++]=e[0]*l+e[2]*m+e[4];g[h++]=e[1]*l+e[3]*m+e[5]}f&&g.length!=h&&(g.length=h);return g};function Sc(){Qc.call(this);this.ka="XY";this.a=2;this.B=null}w(Sc,Qc);function Tc(a){var b;"XY"==a?b=2:"XYZ"==a||"XYM"==a?b=3:"XYZM"==a&&(b=4);return b}k=Sc.prototype;k.zc=oc;k.Nd=function(a){return Sb(this.B,0,this.B.length,this.a,a)};k.Nb=function(){return this.B.slice(0,this.a)};k.la=function(){return this.B};k.Ob=function(){return this.B.slice(this.B.length-this.a)};k.Pb=function(){return this.ka};
k.md=function(a){this.o!=this.g&&(Aa(this.i),this.l=0,this.o=this.g);if(0>a||0!==this.l&&a<=this.l)return this;var b=a.toString();if(this.i.hasOwnProperty(b))return this.i[b];var c=this.Lc(a);if(c.la().length<this.B.length)return this.i[b]=c;this.l=a;return this};k.Lc=function(){return this};k.ua=function(){return this.a};function Uc(a,b,c){a.a=Tc(b);a.ka=b;a.B=c}
function Vc(a,b,c,d){if(b)c=Tc(b);else{for(b=0;b<d;++b){if(0===c.length){a.ka="XY";a.a=2;return}c=c[0]}c=c.length;var e;2==c?e="XY":3==c?e="XYZ":4==c&&(e="XYZM");b=e}a.ka=b;a.a=c}k.rc=function(a){this.B&&(a(this.B,this.B,this.a),this.u())};
k.rotate=function(a,b){var c=this.la();if(c){for(var d=c.length,e=this.ua(),f=c?c:[],g=Math.cos(a),h=Math.sin(a),l=b[0],m=b[1],n=0,p=0;p<d;p+=e){var q=c[p]-l,t=c[p+1]-m;f[n++]=l+q*g-t*h;f[n++]=m+q*h+t*g;for(q=p+2;q<p+e;++q)f[n++]=c[q]}c&&f.length!=n&&(f.length=n);this.u()}};
k.scale=function(a,b,c){var d=b;void 0===d&&(d=a);var e=c;e||(e=gc(this.C()));if(c=this.la()){b=c.length;for(var f=this.ua(),g=c?c:[],h=e[0],e=e[1],l=0,m=0;m<b;m+=f){var n=c[m]-h,p=c[m+1]-e;g[l++]=h+a*n;g[l++]=e+d*p;for(n=m+2;n<m+f;++n)g[l++]=c[n]}c&&g.length!=l&&(g.length=l);this.u()}};k.Qc=function(a,b){var c=this.la();if(c){var d=c.length,e=this.ua(),f=c?c:[],g=0,h,l;for(h=0;h<d;h+=e)for(f[g++]=c[h]+a,f[g++]=c[h+1]+b,l=h+2;l<h+e;++l)f[g++]=c[l];c&&f.length!=g&&(f.length=g);this.u()}};function Wc(a,b,c,d){for(var e=0,f=a[c-d],g=a[c-d+1];b<c;b+=d)var h=a[b],l=a[b+1],e=e+(g*h-f*l),f=h,g=l;return e/2}function Xc(a,b,c,d){var e=0,f,g;f=0;for(g=c.length;f<g;++f){var h=c[f],e=e+Wc(a,b,h,d);b=h}return e};function Yc(a,b,c,d,e,f,g){var h=a[b],l=a[b+1],m=a[c]-h,n=a[c+1]-l;if(0!==m||0!==n)if(f=((e-h)*m+(f-l)*n)/(m*m+n*n),1<f)b=c;else if(0<f){for(e=0;e<d;++e)g[e]=va(a[b+e],a[c+e],f);g.length=d;return}for(e=0;e<d;++e)g[e]=a[b+e];g.length=d}function Zc(a,b,c,d,e){var f=a[b],g=a[b+1];for(b+=d;b<c;b+=d){var h=a[b],l=a[b+1],f=ra(f,g,h,l);f>e&&(e=f);f=h;g=l}return e}function $c(a,b,c,d,e){var f,g;f=0;for(g=c.length;f<g;++f){var h=c[f];e=Zc(a,b,h,d,e);b=h}return e}
function ad(a,b,c,d,e,f,g,h,l,m,n){if(b==c)return m;var p;if(0===e){p=ra(g,h,a[b],a[b+1]);if(p<m){for(n=0;n<d;++n)l[n]=a[b+n];l.length=d;return p}return m}for(var q=n?n:[NaN,NaN],t=b+d;t<c;)if(Yc(a,t-d,t,d,g,h,q),p=ra(g,h,q[0],q[1]),p<m){m=p;for(n=0;n<d;++n)l[n]=q[n];l.length=d;t+=d}else t+=d*Math.max((Math.sqrt(p)-Math.sqrt(m))/e|0,1);if(f&&(Yc(a,c-d,b,d,g,h,q),p=ra(g,h,q[0],q[1]),p<m)){m=p;for(n=0;n<d;++n)l[n]=q[n];l.length=d}return m}
function bd(a,b,c,d,e,f,g,h,l,m,n){n=n?n:[NaN,NaN];var p,q;p=0;for(q=c.length;p<q;++p){var t=c[p];m=ad(a,b,t,d,e,f,g,h,l,m,n);b=t}return m};function cd(a,b){var c=0,d,e;d=0;for(e=b.length;d<e;++d)a[c++]=b[d];return c}function dd(a,b,c,d){var e,f;e=0;for(f=c.length;e<f;++e){var g=c[e],h;for(h=0;h<d;++h)a[b++]=g[h]}return b}function ed(a,b,c,d,e){e=e?e:[];var f=0,g,h;g=0;for(h=c.length;g<h;++g)b=dd(a,b,c[g],d),e[f++]=b;e.length=f;return e};function fd(a,b,c,d,e){e=void 0!==e?e:[];for(var f=0;b<c;b+=d)e[f++]=a.slice(b,b+d);e.length=f;return e}function gd(a,b,c,d,e){e=void 0!==e?e:[];var f=0,g,h;g=0;for(h=c.length;g<h;++g){var l=c[g];e[f++]=fd(a,b,l,d,e[f]);b=l}e.length=f;return e};function hd(a,b,c,d,e,f,g){var h=(c-b)/d;if(3>h){for(;b<c;b+=d)f[g++]=a[b],f[g++]=a[b+1];return g}var l=Array(h);l[0]=1;l[h-1]=1;c=[b,c-d];for(var m=0,n;0<c.length;){var p=c.pop(),q=c.pop(),t=0,v=a[q],u=a[q+1],y=a[p],E=a[p+1];for(n=q+d;n<p;n+=d){var z=qa(a[n],a[n+1],v,u,y,E);z>t&&(m=n,t=z)}t>e&&(l[(m-b)/d]=1,q+d<m&&c.push(q,m),m+d<p&&c.push(m,p))}for(n=0;n<h;++n)l[n]&&(f[g++]=a[b+n*d],f[g++]=a[b+n*d+1]);return g}
function id(a,b,c,d,e,f,g,h){var l,m;l=0;for(m=c.length;l<m;++l){var n=c[l];a:{var p=a,q=n,t=d,v=e,u=f;if(b!=q){var y=v*Math.round(p[b]/v),E=v*Math.round(p[b+1]/v);b+=t;u[g++]=y;u[g++]=E;var z,G;do if(z=v*Math.round(p[b]/v),G=v*Math.round(p[b+1]/v),b+=t,b==q){u[g++]=z;u[g++]=G;break a}while(z==y&&G==E);for(;b<q;){var J,X;J=v*Math.round(p[b]/v);X=v*Math.round(p[b+1]/v);b+=t;if(J!=z||X!=G){var A=z-y,Ma=G-E,ua=J-y,M=X-E;A*M==Ma*ua&&(0>A&&ua<A||A==ua||0<A&&ua>A)&&(0>Ma&&M<Ma||Ma==M||0<Ma&&M>Ma)||(u[g++]=
z,u[g++]=G,y=z,E=G);z=J;G=X}}u[g++]=z;u[g++]=G}}h.push(g);b=n}return g};function jd(a,b){Sc.call(this);this.c=this.j=-1;this.sa(a,b)}w(jd,Sc);k=jd.prototype;k.clone=function(){var a=new jd(null);kd(a,this.ka,this.B.slice());return a};k.vb=function(a,b,c,d){if(d<Kb(this.C(),a,b))return d;this.c!=this.g&&(this.j=Math.sqrt(Zc(this.B,0,this.B.length,this.a,0)),this.c=this.g);return ad(this.B,0,this.B.length,this.a,this.j,!0,a,b,c,d)};k.im=function(){return Wc(this.B,0,this.B.length,this.a)};k.Z=function(){return fd(this.B,0,this.B.length,this.a)};
k.Lc=function(a){var b=[];b.length=hd(this.B,0,this.B.length,this.a,a,b,0);a=new jd(null);kd(a,"XY",b);return a};k.X=function(){return"LinearRing"};k.sa=function(a,b){a?(Vc(this,b,a,1),this.B||(this.B=[]),this.B.length=dd(this.B,0,a,this.a),this.u()):kd(this,"XY",null)};function kd(a,b,c){Uc(a,b,c);a.u()};function C(a,b){Sc.call(this);this.sa(a,b)}w(C,Sc);k=C.prototype;k.clone=function(){var a=new C(null);a.ba(this.ka,this.B.slice());return a};k.vb=function(a,b,c,d){var e=this.B;a=ra(a,b,e[0],e[1]);if(a<d){d=this.a;for(b=0;b<d;++b)c[b]=e[b];c.length=d;return a}return d};k.Z=function(){return this.B?this.B.slice():[]};k.Nd=function(a){return Rb(this.B,a)};k.X=function(){return"Point"};k.La=function(a){return Mb(a,this.B[0],this.B[1])};
k.sa=function(a,b){a?(Vc(this,b,a,0),this.B||(this.B=[]),this.B.length=cd(this.B,a),this.u()):this.ba("XY",null)};k.ba=function(a,b){Uc(this,a,b);this.u()};function ld(a,b,c,d,e){return!Xb(e,function(e){return!md(a,b,c,d,e[0],e[1])})}function md(a,b,c,d,e,f){for(var g=!1,h=a[c-d],l=a[c-d+1];b<c;b+=d){var m=a[b],n=a[b+1];l>f!=n>f&&e<(m-h)*(f-l)/(n-l)+h&&(g=!g);h=m;l=n}return g}function nd(a,b,c,d,e,f){if(0===c.length||!md(a,b,c[0],d,e,f))return!1;var g;b=1;for(g=c.length;b<g;++b)if(md(a,c[b-1],c[b],d,e,f))return!1;return!0};function od(a,b,c,d,e,f,g){var h,l,m,n,p,q=e[f+1],t=[],v=c[0];m=a[v-d];p=a[v-d+1];for(h=b;h<v;h+=d){n=a[h];l=a[h+1];if(q<=p&&l<=q||p<=q&&q<=l)m=(q-p)/(l-p)*(n-m)+m,t.push(m);m=n;p=l}v=NaN;p=-Infinity;t.sort(ab);m=t[0];h=1;for(l=t.length;h<l;++h){n=t[h];var u=Math.abs(n-m);u>p&&(m=(m+n)/2,nd(a,b,c,d,m,q)&&(v=m,p=u));m=n}isNaN(v)&&(v=e[f]);return g?(g.push(v,q),g):[v,q]};function pd(a,b,c,d,e,f){for(var g=[a[b],a[b+1]],h=[],l;b+d<c;b+=d){h[0]=a[b+d];h[1]=a[b+d+1];if(l=e.call(f,g,h))return l;g[0]=h[0];g[1]=h[1]}return!1};function qd(a,b,c,d,e){var f=Tb(Eb(),a,b,c,d);return kc(e,f)?Nb(e,f)||f[0]>=e[0]&&f[2]<=e[2]||f[1]>=e[1]&&f[3]<=e[3]?!0:pd(a,b,c,d,function(a,b){var c=!1,d=Ob(e,a),f=Ob(e,b);if(1===d||1===f)c=!0;else{var p=e[0],q=e[1],t=e[2],v=e[3],u=b[0],y=b[1],E=(y-a[1])/(u-a[0]);f&2&&!(d&2)&&(c=u-(y-v)/E,c=c>=p&&c<=t);c||!(f&4)||d&4||(c=y-(u-t)*E,c=c>=q&&c<=v);c||!(f&8)||d&8||(c=u-(y-q)/E,c=c>=p&&c<=t);c||!(f&16)||d&16||(c=y-(u-p)*E,c=c>=q&&c<=v)}return c}):!1}
function rd(a,b,c,d,e){var f=c[0];if(!(qd(a,b,f,d,e)||md(a,b,f,d,e[0],e[1])||md(a,b,f,d,e[0],e[3])||md(a,b,f,d,e[2],e[1])||md(a,b,f,d,e[2],e[3])))return!1;if(1===c.length)return!0;b=1;for(f=c.length;b<f;++b)if(ld(a,c[b-1],c[b],d,e))return!1;return!0};function sd(a,b,c,d){for(var e=0,f=a[c-d],g=a[c-d+1];b<c;b+=d)var h=a[b],l=a[b+1],e=e+(h-f)*(l+g),f=h,g=l;return 0<e}function td(a,b,c,d){var e=0;d=void 0!==d?d:!1;var f,g;f=0;for(g=b.length;f<g;++f){var h=b[f],e=sd(a,e,h,c);if(0===f){if(d&&e||!d&&!e)return!1}else if(d&&!e||!d&&e)return!1;e=h}return!0}
function ud(a,b,c,d,e){e=void 0!==e?e:!1;var f,g;f=0;for(g=c.length;f<g;++f){var h=c[f],l=sd(a,b,h,d);if(0===f?e&&l||!e&&!l:e&&!l||!e&&l)for(var l=a,m=h,n=d;b<m-n;){var p;for(p=0;p<n;++p){var q=l[b+p];l[b+p]=l[m-n+p];l[m-n+p]=q}b+=n;m-=n}b=h}return b}function vd(a,b,c,d){var e=0,f,g;f=0;for(g=b.length;f<g;++f)e=ud(a,e,b[f],c,d);return e};function D(a,b){Sc.call(this);this.c=[];this.A=-1;this.H=null;this.R=this.D=this.O=-1;this.j=null;this.sa(a,b)}w(D,Sc);k=D.prototype;k.uj=function(a){this.B?eb(this.B,a.la()):this.B=a.la().slice();this.c.push(this.B.length);this.u()};k.clone=function(){var a=new D(null);a.ba(this.ka,this.B.slice(),this.c.slice());return a};
k.vb=function(a,b,c,d){if(d<Kb(this.C(),a,b))return d;this.D!=this.g&&(this.O=Math.sqrt($c(this.B,0,this.c,this.a,0)),this.D=this.g);return bd(this.B,0,this.c,this.a,this.O,!0,a,b,c,d)};k.zc=function(a,b){return nd(this.Qb(),0,this.c,this.a,a,b)};k.lm=function(){return Xc(this.Qb(),0,this.c,this.a)};k.Z=function(a){var b;void 0!==a?(b=this.Qb().slice(),ud(b,0,this.c,this.a,a)):b=this.B;return gd(b,0,this.c,this.a)};k.Hb=function(){return this.c};
function wd(a){if(a.A!=a.g){var b=gc(a.C());a.H=od(a.Qb(),0,a.c,a.a,b,0);a.A=a.g}return a.H}k.Zj=function(){return new C(wd(this))};k.dk=function(){return this.c.length};k.Fg=function(a){if(0>a||this.c.length<=a)return null;var b=new jd(null);kd(b,this.ka,this.B.slice(0===a?0:this.c[a-1],this.c[a]));return b};k.Vd=function(){var a=this.ka,b=this.B,c=this.c,d=[],e=0,f,g;f=0;for(g=c.length;f<g;++f){var h=c[f],l=new jd(null);kd(l,a,b.slice(e,h));d.push(l);e=h}return d};
k.Qb=function(){if(this.R!=this.g){var a=this.B;td(a,this.c,this.a)?this.j=a:(this.j=a.slice(),this.j.length=ud(this.j,0,this.c,this.a));this.R=this.g}return this.j};k.Lc=function(a){var b=[],c=[];b.length=id(this.B,0,this.c,this.a,Math.sqrt(a),b,0,c);a=new D(null);a.ba("XY",b,c);return a};k.X=function(){return"Polygon"};k.La=function(a){return rd(this.Qb(),0,this.c,this.a,a)};
k.sa=function(a,b){if(a){Vc(this,b,a,2);this.B||(this.B=[]);var c=ed(this.B,0,a,this.a,this.c);this.B.length=0===c.length?0:c[c.length-1];this.u()}else this.ba("XY",null,this.c)};k.ba=function(a,b,c){Uc(this,a,b);this.c=c;this.u()};function xd(a,b,c,d){var e=d?d:32;d=[];var f;for(f=0;f<e;++f)eb(d,a.offset(b,c,2*Math.PI*f/e));d.push(d[0],d[1]);a=new D(null);a.ba("XY",d,[d.length]);return a}
function yd(a){var b=a[0],c=a[1],d=a[2];a=a[3];b=[b,c,b,a,d,a,d,c,b,c];c=new D(null);c.ba("XY",b,[b.length]);return c}function zd(a,b,c){var d=b?b:32,e=a.ua();b=a.ka;for(var f=new D(null,b),d=e*(d+1),e=Array(d),g=0;g<d;g++)e[g]=0;f.ba(b,e,[e.length]);Ad(f,a.qd(),a.vf(),c);return f}function Ad(a,b,c,d){var e=a.la(),f=a.ka,g=a.ua(),h=a.Hb(),l=e.length/g-1;d=d?d:0;for(var m,n,p=0;p<=l;++p)n=p*g,m=d+2*ta(p,l)*Math.PI/l,e[n]=b[0]+c*Math.cos(m),e[n+1]=b[1]+c*Math.sin(m);a.ba(f,e,h)};function Bd(a){Xa.call(this);a=a||{};this.c=[0,0];var b={};b[Cd]=void 0!==a.center?a.center:null;this.j=Jc(a.projection);var c,d,e,f=void 0!==a.minZoom?a.minZoom:0;c=void 0!==a.maxZoom?a.maxZoom:28;var g=void 0!==a.zoomFactor?a.zoomFactor:2;if(void 0!==a.resolutions)c=a.resolutions,d=c[0],e=c[c.length-1],c=mb(c);else{d=Jc(a.projection);e=d.C();var h=(e?Math.max(ec(e),fc(e)):360*rc.degrees/d.cc())/256/Math.pow(2,0),l=h/Math.pow(2,28);d=a.maxResolution;void 0!==d?f=0:d=h/Math.pow(g,f);e=a.minResolution;
void 0===e&&(e=void 0!==a.maxZoom?void 0!==a.maxResolution?d/Math.pow(g,c):h/Math.pow(g,c):l);c=f+Math.floor(Math.log(d/e)/Math.log(g));e=d/Math.pow(g,c-f);c=nb(g,d,c-f)}this.a=d;this.i=e;this.s=g;this.f=a.resolutions;this.l=f;f=void 0!==a.extent?wa(a.extent):xa;(void 0!==a.enableRotation?a.enableRotation:1)?(g=a.constrainRotation,g=void 0===g||!0===g?rb():!1===g?pb:"number"===typeof g?qb(g):pb):g=ob;this.o=new ya(f,c,g);void 0!==a.resolution?b[Dd]=a.resolution:void 0!==a.zoom&&(b[Dd]=this.constrainResolution(this.a,
a.zoom-this.l));b[Ed]=void 0!==a.rotation?a.rotation:0;this.G(b)}w(Bd,Xa);k=Bd.prototype;k.Od=function(a){return this.o.center(a)};k.constrainResolution=function(a,b,c){return this.o.resolution(a,b||0,c||0)};k.constrainRotation=function(a,b){return this.o.rotation(a,b||0)};k.bb=function(){return this.get(Cd)};function Fd(a,b){return void 0!==b?(b[0]=a.c[0],b[1]=a.c[1],b):a.c.slice()}
k.Ic=function(a){var b=this.bb();la(b,1);var c=this.$();la(void 0!==c,2);var d=this.Ma();la(void 0!==d,3);return ic(b,c,d,a)};k.Ql=function(){return this.a};k.Rl=function(){return this.i};k.Sl=function(){return this.j};k.$=function(){return this.get(Dd)};k.Tl=function(){return this.f};function Gd(a,b){return Math.max(ec(a)/b[0],fc(a)/b[1])}function Hd(a){var b=a.a,c=Math.log(b/a.i)/Math.log(2);return function(a){return b/Math.pow(2,a*c)}}k.Ma=function(){return this.get(Ed)};
function Id(a){var b=a.a,c=Math.log(b/a.i)/Math.log(2);return function(a){return Math.log(b/a)/Math.log(2)/c}}k.V=function(){var a=this.bb(),b=this.j,c=this.$(),d=this.Ma();return{center:a.slice(),projection:void 0!==b?b:null,resolution:c,rotation:d}};k.Ck=function(){var a,b=this.$();if(void 0!==b&&b>=this.i&&b<=this.a){a=this.l||0;var c,d;if(this.f){d=cb(this.f,b,1);a+=d;if(d==this.f.length-1)return a;c=this.f[d];d=c/this.f[d+1]}else c=this.a,d=this.s;a+=Math.log(c/b)/Math.log(d)}return a};
k.af=function(a,b,c){a instanceof Sc||(la(Array.isArray(a),24),la(!dc(a),25),a=yd(a));var d=c||{};c=void 0!==d.padding?d.padding:[0,0,0,0];var e=void 0!==d.constrainResolution?d.constrainResolution:!0,f=void 0!==d.nearest?d.nearest:!1,g;void 0!==d.minResolution?g=d.minResolution:void 0!==d.maxZoom?g=this.constrainResolution(this.a,d.maxZoom-this.l,0):g=0;var h=a.la(),l=this.Ma(),d=Math.cos(-l),l=Math.sin(-l),m=Infinity,n=Infinity,p=-Infinity,q=-Infinity;a=a.ua();for(var t=0,v=h.length;t<v;t+=a)var u=
h[t]*d-h[t+1]*l,y=h[t]*l+h[t+1]*d,m=Math.min(m,u),n=Math.min(n,y),p=Math.max(p,u),q=Math.max(q,y);b=Gd([m,n,p,q],[b[0]-c[1]-c[3],b[1]-c[0]-c[2]]);b=isNaN(b)?g:Math.max(b,g);e&&(g=this.constrainResolution(b,0,0),!f&&g<b&&(g=this.constrainResolution(g,-1,0)),b=g);this.Zb(b);l=-l;f=(m+p)/2+(c[1]-c[3])/2*b;c=(n+q)/2+(c[0]-c[2])/2*b;this.ob([f*d-c*l,c*d+f*l])};
k.Aj=function(a,b,c){var d=this.Ma(),e=Math.cos(-d),d=Math.sin(-d),f=a[0]*e-a[1]*d;a=a[1]*e+a[0]*d;var g=this.$(),f=f+(b[0]/2-c[0])*g;a+=(c[1]-b[1]/2)*g;d=-d;this.ob([f*e-a*d,a*e+f*d])};function Jd(a){return!!a.bb()&&void 0!==a.$()}k.rotate=function(a,b){if(void 0!==b){var c,d=this.bb();void 0!==d&&(c=[d[0]-b[0],d[1]-b[1]],zb(c,a-this.Ma()),ub(c,b));this.ob(c)}this.ge(a)};k.ob=function(a){this.set(Cd,a)};function Kd(a,b){a.c[1]+=b}k.Zb=function(a){this.set(Dd,a)};k.ge=function(a){this.set(Ed,a)};
k.kp=function(a){a=this.constrainResolution(this.a,a-this.l,0);this.Zb(a)};var Cd="center",Dd="resolution",Ed="rotation";function Ld(a){return Math.pow(a,3)}function Md(a){return 1-Ld(1-a)}function Nd(a){return 3*a*a-2*a*a*a}function Qd(a){return a}function Rd(a){return.5>a?Nd(2*a):1-Nd(2*(a-.5))};function Sd(a){var b=a.source,c=a.start?a.start:Date.now(),d=b[0],e=b[1],f=void 0!==a.duration?a.duration:1E3,g=a.easing?a.easing:Nd;return function(a,b){if(b.time<c)return b.animate=!0,b.viewHints[0]+=1,!0;if(b.time<c+f){var m=1-g((b.time-c)/f),n=d-b.viewState.center[0],p=e-b.viewState.center[1];b.animate=!0;b.viewState.center[0]+=m*n;b.viewState.center[1]+=m*p;b.viewHints[0]+=1;return!0}return!1}}
function Td(a){var b=a.rotation?a.rotation:0,c=a.start?a.start:Date.now(),d=void 0!==a.duration?a.duration:1E3,e=a.easing?a.easing:Nd,f=a.anchor?a.anchor:null;return function(a,h){if(h.time<c)return h.animate=!0,h.viewHints[0]+=1,!0;if(h.time<c+d){var l=1-e((h.time-c)/d),l=(b-h.viewState.rotation)*l;h.animate=!0;h.viewState.rotation+=l;if(f){var m=h.viewState.center;m[0]-=f[0];m[1]-=f[1];zb(m,l);ub(m,f)}h.viewHints[0]+=1;return!0}return!1}}
function Ud(a){var b=a.resolution,c=a.start?a.start:Date.now(),d=void 0!==a.duration?a.duration:1E3,e=a.easing?a.easing:Nd;return function(a,g){if(g.time<c)return g.animate=!0,g.viewHints[0]+=1,!0;if(g.time<c+d){var h=1-e((g.time-c)/d),l=b-g.viewState.resolution;g.animate=!0;g.viewState.resolution+=h*l;g.viewHints[0]+=1;return!0}return!1}};function Vd(a,b,c,d){this.ca=a;this.ea=b;this.fa=c;this.ia=d}function Wd(a,b,c){return a.ca<=b&&b<=a.ea&&a.fa<=c&&c<=a.ia}function Xd(a,b){return a.ca==b.ca&&a.fa==b.fa&&a.ea==b.ea&&a.ia==b.ia}function Yd(a,b){return a.ca<=b.ea&&a.ea>=b.ca&&a.fa<=b.ia&&a.ia>=b.fa};function Zd(a,b,c){void 0===c&&(c=[0,0]);c[0]=a[0]+2*b;c[1]=a[1]+2*b;return c}function $d(a,b,c){void 0===c&&(c=[0,0]);c[0]=a[0]*b+.5|0;c[1]=a[1]*b+.5|0;return c}function ae(a,b){if(Array.isArray(a))return a;void 0===b?b=[a,a]:b[0]=b[1]=a;return b};function be(a,b,c,d){return void 0!==d?(d[0]=a,d[1]=b,d[2]=c,d):[a,b,c]}function ce(a){var b=a[0],c=Array(b),d=1<<b-1,e,f;for(e=0;e<b;++e)f=48,a[1]&d&&(f+=1),a[2]&d&&(f+=2),c[e]=String.fromCharCode(f),d>>=1;return c.join("")};function de(a){this.minZoom=void 0!==a.minZoom?a.minZoom:0;this.b=a.resolutions;la(lb(this.b,function(a,b){return b-a}),17);this.maxZoom=this.b.length-1;this.g=void 0!==a.origin?a.origin:null;this.f=null;void 0!==a.origins&&(this.f=a.origins,la(this.f.length==this.b.length,20));var b=a.extent;void 0===b||this.g||this.f||(this.g=bc(b));la(!this.g&&this.f||this.g&&!this.f,18);this.c=null;void 0!==a.tileSizes&&(this.c=a.tileSizes,la(this.c.length==this.b.length,19));this.i=void 0!==a.tileSize?a.tileSize:
this.c?null:256;la(!this.i&&this.c||this.i&&!this.c,22);this.s=void 0!==b?b:null;this.a=null;this.l=[0,0];void 0!==a.sizes?this.a=a.sizes.map(function(a){return new Vd(Math.min(0,a[0]),Math.max(a[0]-1,-1),Math.min(0,a[1]),Math.max(a[1]-1,-1))},this):b&&ee(this,b)}var fe=[0,0,0];k=de.prototype;k.wg=function(a,b,c){a=ge(this,a,b);for(var d=a.ca,e=a.ea;d<=e;++d)for(var f=a.fa,g=a.ia;f<=g;++f)c([b,d,f])};
function he(a,b,c,d,e){e=a.Ea(b,e);for(b=b[0]-1;b>=a.minZoom;){if(c.call(null,b,ge(a,e,b,d)))return!0;--b}return!1}k.C=function(){return this.s};k.Gg=function(){return this.maxZoom};k.Hg=function(){return this.minZoom};k.Ka=function(a){return this.g?this.g:this.f[a]};k.$=function(a){return this.b[a]};k.Hh=function(){return this.b};function ie(a,b,c,d){return b[0]<a.maxZoom?(d=a.Ea(b,d),ge(a,d,b[0]+1,c)):null}
function je(a,b,c,d){ke(a,b[0],b[1],c,!1,fe);var e=fe[1],f=fe[2];ke(a,b[2],b[3],c,!0,fe);a=fe[1];b=fe[2];void 0!==d?(d.ca=e,d.ea=a,d.fa=f,d.ia=b):d=new Vd(e,a,f,b);return d}function ge(a,b,c,d){c=a.$(c);return je(a,b,c,d)}function le(a,b){var c=a.Ka(b[0]),d=a.$(b[0]),e=ae(a.Qa(b[0]),a.l);return[c[0]+(b[1]+.5)*e[0]*d,c[1]+(b[2]+.5)*e[1]*d]}k.Ea=function(a,b){var c=this.Ka(a[0]),d=this.$(a[0]),e=ae(this.Qa(a[0]),this.l),f=c[0]+a[1]*e[0]*d,c=c[1]+a[2]*e[1]*d;return Qb(f,c,f+e[0]*d,c+e[1]*d,b)};
k.Yd=function(a,b,c){return ke(this,a[0],a[1],b,!1,c)};function ke(a,b,c,d,e,f){var g=a.ec(d),h=d/a.$(g),l=a.Ka(g);a=ae(a.Qa(g),a.l);b=h*Math.floor((b-l[0])/d+(e?.5:0))/a[0];c=h*Math.floor((c-l[1])/d+(e?0:.5))/a[1];e?(b=Math.ceil(b)-1,c=Math.ceil(c)-1):(b=Math.floor(b),c=Math.floor(c));return be(g,b,c,f)}k.pd=function(a,b,c){b=this.$(b);return ke(this,a[0],a[1],b,!1,c)};k.Qa=function(a){return this.i?this.i:this.c[a]};k.ec=function(a,b){return ma(cb(this.b,a,b||0),this.minZoom,this.maxZoom)};
function ee(a,b){for(var c=a.b.length,d=Array(c),e=a.minZoom;e<c;++e)d[e]=ge(a,b,e);a.a=d};function me(a){var b=a.l;if(!b){var b=ne(a),c=oe(b,void 0,void 0),b=new de({extent:b,origin:bc(b),resolutions:c,tileSize:void 0});a.l=b}return b}function pe(a){var b={};za(b,void 0!==a?a:{});void 0===b.extent&&(b.extent=vc("EPSG:3857").C());b.resolutions=oe(b.extent,b.maxZoom,b.tileSize);delete b.maxZoom;return new de(b)}function oe(a,b,c){b=void 0!==b?b:42;var d=fc(a);a=ec(a);c=ae(void 0!==c?c:256);c=Math.max(a/c[0],d/c[1]);b+=1;d=Array(b);for(a=0;a<b;++a)d[a]=c/Math.pow(2,a);return d}
function ne(a){a=vc(a);var b=a.C();b||(a=180*rc.degrees/a.cc(),b=Qb(-a,-a,a,a));return b};function qe(a){this.a=a.html;this.b=a.tileRanges?a.tileRanges:null}qe.prototype.g=function(){return this.a};function re(a){Xa.call(this);this.a=a?a:[];se(this)}w(re,Xa);k=re.prototype;k.clear=function(){for(;0<this.gc();)this.pop()};k.pf=function(a){var b,c;b=0;for(c=a.length;b<c;++b)this.push(a[b]);return this};k.forEach=function(a,b){this.a.forEach(a,b)};k.Bl=function(){return this.a};k.item=function(a){return this.a[a]};k.gc=function(){return this.get("length")};k.ce=function(a,b){this.a.splice(a,0,b);se(this);this.b(new te(ue,b))};k.pop=function(){return this.Qf(this.gc()-1)};
k.push=function(a){var b=this.a.length;this.ce(b,a);return b};k.remove=function(a){var b=this.a,c,d;c=0;for(d=b.length;c<d;++c)if(b[c]===a)return this.Qf(c)};k.Qf=function(a){var b=this.a[a];this.a.splice(a,1);se(this);this.b(new te(ve,b));return b};k.Wo=function(a,b){var c=this.gc();if(a<c)c=this.a[a],this.a[a]=b,this.b(new te(ve,c)),this.b(new te(ue,b));else{for(;c<a;++c)this.ce(c,void 0);this.ce(a,b)}};function se(a){a.set("length",a.a.length)}var ue="add",ve="remove";
function te(a,b){Pa.call(this,a);this.element=b}w(te,Pa);var we=/^#(?:[0-9a-f]{3}){1,2}$/i,xe=/^(?:rgb)?\((0|[1-9]\d{0,2}),\s?(0|[1-9]\d{0,2}),\s?(0|[1-9]\d{0,2})\)$/i,ye=/^(?:rgba)?\((0|[1-9]\d{0,2}),\s?(0|[1-9]\d{0,2}),\s?(0|[1-9]\d{0,2}),\s?(0|1|0\.\d{0,10})\)$/i,ze=/^([a-z]*)$/i;function Ae(a){return Array.isArray(a)?a:Be(a)}function Ce(a){if("string"!==typeof a){var b=a[0];b!=(b|0)&&(b=b+.5|0);var c=a[1];c!=(c|0)&&(c=c+.5|0);var d=a[2];d!=(d|0)&&(d=d+.5|0);a="rgba("+b+","+c+","+d+","+(void 0===a[3]?1:a[3])+")"}return a}
var Be=function(){var a={},b=0;return function(c){var d;if(a.hasOwnProperty(c))d=a[c];else{if(1024<=b){d=0;for(var e in a)0===(d++&3)&&(delete a[e],--b)}d=c;var f,g;ze.exec(d)&&(e=document.createElement("div"),e.style.color=d,document.body.appendChild(e),d=window.getComputedStyle(e).color,document.body.removeChild(e));we.exec(d)?(f=d.length-1,la(3==f||6==f,54),g=3==f?1:2,f=parseInt(d.substr(1+0*g,g),16),e=parseInt(d.substr(1+1*g,g),16),d=parseInt(d.substr(1+2*g,g),16),1==g&&(f=(f<<4)+f,e=(e<<4)+e,
d=(d<<4)+d),f=[f,e,d,1]):(g=ye.exec(d))?(f=Number(g[1]),e=Number(g[2]),d=Number(g[3]),g=Number(g[4]),f=De([f,e,d,g])):(g=xe.exec(d))?(f=Number(g[1]),e=Number(g[2]),d=Number(g[3]),f=De([f,e,d,1])):la(!1,14);d=f;a[c]=d;++b}return d}}();function De(a){var b=[];b[0]=ma(a[0]+.5|0,0,255);b[1]=ma(a[1]+.5|0,0,255);b[2]=ma(a[2]+.5|0,0,255);b[3]=ma(a[3],0,1);return b};function Ee(a){return"string"===typeof a||a instanceof CanvasPattern||a instanceof CanvasGradient?a:Ce(a)};var Fe=String.prototype.trim?function(a){return a.trim()}:function(a){return a.replace(/^[\s\xa0]+|[\s\xa0]+$/g,"")};function Ge(a,b){return a<b?-1:a>b?1:0};function He(a,b,c){return 2>=arguments.length?Array.prototype.slice.call(a,b):Array.prototype.slice.call(a,b,c)};var Ie;a:{var Je=aa.navigator;if(Je){var Ke=Je.userAgent;if(Ke){Ie=Ke;break a}}Ie=""}function Le(a){return-1!=Ie.indexOf(a)};var Me=Le("Opera"),Ne=Le("Trident")||Le("MSIE"),Oe=Le("Edge"),Pe=Le("Gecko")&&!(-1!=Ie.toLowerCase().indexOf("webkit")&&!Le("Edge"))&&!(Le("Trident")||Le("MSIE"))&&!Le("Edge"),Qe=-1!=Ie.toLowerCase().indexOf("webkit")&&!Le("Edge"),Re;
a:{var Se="",Te=function(){var a=Ie;if(Pe)return/rv\:([^\);]+)(\)|;)/.exec(a);if(Oe)return/Edge\/([\d\.]+)/.exec(a);if(Ne)return/\b(?:MSIE|rv)[: ]([^\);]+)(\)|;)/.exec(a);if(Qe)return/WebKit\/(\S+)/.exec(a);if(Me)return/(?:Version)[ \/]?(\S+)/.exec(a)}();Te&&(Se=Te?Te[1]:"");if(Ne){var Ue,Ve=aa.document;Ue=Ve?Ve.documentMode:void 0;if(null!=Ue&&Ue>parseFloat(Se)){Re=String(Ue);break a}}Re=Se}var We={};function Xe(){return[1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1]}function Ye(a,b){a[0]=b[0];a[1]=b[1];a[4]=b[2];a[5]=b[3];a[12]=b[4];a[13]=b[5];return a};var Ze=Xe();function $e(a,b){var c=document.createElement("CANVAS");a&&(c.width=a);b&&(c.height=b);return c.getContext("2d")}
var af=function(){var a;return function(){if(void 0===a){var b=document.createElement("P"),c,d={webkitTransform:"-webkit-transform",OTransform:"-o-transform",msTransform:"-ms-transform",MozTransform:"-moz-transform",transform:"transform"};document.body.appendChild(b);for(var e in d)e in b.style&&(b.style[e]="translate(1px,1px)",c=ja.getComputedStyle(b).getPropertyValue(d[e]));document.body.removeChild(b);a=c&&"none"!==c}return a}}(),bf=function(){var a;return function(){if(void 0===a){var b=document.createElement("P"),
c,d={webkitTransform:"-webkit-transform",OTransform:"-o-transform",msTransform:"-ms-transform",MozTransform:"-moz-transform",transform:"transform"};document.body.appendChild(b);for(var e in d)e in b.style&&(b.style[e]="translate3d(1px,1px,1px)",c=ja.getComputedStyle(b).getPropertyValue(d[e]));document.body.removeChild(b);a=c&&"none"!==c}return a}}();
function cf(a,b){var c=a.style;c.WebkitTransform=b;c.MozTransform=b;c.b=b;c.msTransform=b;c.transform=b;if((c=Ne)&&!(c=We["9.0"])){for(var c=0,d=Fe(String(Re)).split("."),e=Fe("9.0").split("."),f=Math.max(d.length,e.length),g=0;0==c&&g<f;g++){var h=d[g]||"",l=e[g]||"",m=RegExp("(\\d*)(\\D*)","g"),n=RegExp("(\\d*)(\\D*)","g");do{var p=m.exec(h)||["","",""],q=n.exec(l)||["","",""];if(0==p[0].length&&0==q[0].length)break;c=Ge(0==p[1].length?0:parseInt(p[1],10),0==q[1].length?0:parseInt(q[1],10))||Ge(0==
p[2].length,0==q[2].length)||Ge(p[2],q[2])}while(0==c)}c=We["9.0"]=0<=c}c&&(a.style.transformOrigin="0 0")}function df(a,b){var c;if(bf()){var d=Ye(Ze,b),e=Array(16);for(c=0;16>c;++c)e[c]=d[c].toFixed(6);cf(a,"matrix3d("+e.join(",")+")")}else if(af()){d=Array(6);for(c=0;6>c;++c)d[c]=b[c].toFixed(6);cf(a,"matrix("+d.join(",")+")")}else a.style.left=Math.round(b[4])+"px",a.style.top=Math.round(b[5])+"px"}function ef(a,b){var c=b.parentNode;c&&c.replaceChild(a,b)}
function ff(a){a&&a.parentNode&&a.parentNode.removeChild(a)}function gf(a){for(;a.lastChild;)a.removeChild(a.lastChild)};function hf(a,b,c){Pa.call(this,a);this.map=b;this.frameState=void 0!==c?c:null}w(hf,Pa);function jf(a){Xa.call(this);this.element=a.element?a.element:null;this.a=this.R=null;this.s=[];this.render=a.render?a.render:ha;a.target&&this.c(a.target)}w(jf,Xa);jf.prototype.ma=function(){ff(this.element);Xa.prototype.ma.call(this)};jf.prototype.i=function(){return this.a};
jf.prototype.setMap=function(a){this.a&&ff(this.element);for(var b=0,c=this.s.length;b<c;++b)Ea(this.s[b]);this.s.length=0;if(this.a=a)(this.R?this.R:a.v).appendChild(this.element),this.render!==ha&&this.s.push(B(a,"postrender",this.render,this)),a.render()};jf.prototype.c=function(a){this.R="string"===typeof a?document.getElementById(a):a};function kf(a){a=a?a:{};this.O=document.createElement("UL");this.v=document.createElement("LI");this.O.appendChild(this.v);this.v.style.display="none";this.f=void 0!==a.collapsed?a.collapsed:!0;this.j=void 0!==a.collapsible?a.collapsible:!0;this.j||(this.f=!1);var b=void 0!==a.className?a.className:"ol-attribution",c=void 0!==a.tipLabel?a.tipLabel:"Attributions",d=void 0!==a.collapseLabel?a.collapseLabel:"\u00bb";"string"===typeof d?(this.A=document.createElement("span"),this.A.textContent=d):this.A=
d;d=void 0!==a.label?a.label:"i";"string"===typeof d?(this.H=document.createElement("span"),this.H.textContent=d):this.H=d;var e=this.j&&!this.f?this.A:this.H,d=document.createElement("button");d.setAttribute("type","button");d.title=c;d.appendChild(e);B(d,"click",this.Wl,this);c=document.createElement("div");c.className=b+" ol-unselectable ol-control"+(this.f&&this.j?" ol-collapsed":"")+(this.j?"":" ol-uncollapsible");c.appendChild(this.O);c.appendChild(d);jf.call(this,{element:c,render:a.render?
a.render:lf,target:a.target});this.D=!0;this.o={};this.l={};this.T={}}w(kf,jf);
function lf(a){if(a=a.frameState){var b,c,d,e,f,g,h,l,m,n,p,q=a.layerStatesArray,t=za({},a.attributions),v={},u=a.viewState.projection;c=0;for(b=q.length;c<b;c++)if(g=q[c].layer.ga())if(n=x(g).toString(),m=g.l)for(d=0,e=m.length;d<e;d++)if(h=m[d],l=x(h).toString(),!(l in t)){if(f=a.usedTiles[n]){var y=g.fb(u);a:{p=h;var E=u;if(p.b){var z,G,J,X=void 0;for(X in f)if(X in p.b){J=f[X];var A;z=0;for(G=p.b[X].length;z<G;++z){A=p.b[X][z];if(Yd(A,J)){p=!0;break a}var Ma=ge(y,ne(E),parseInt(X,10)),ua=Ma.ea-
Ma.ca+1;if(J.ca<Ma.ca||J.ea>Ma.ea)if(Yd(A,new Vd(ta(J.ca,ua),ta(J.ea,ua),J.fa,J.ia))||J.ea-J.ca+1>ua&&Yd(A,Ma)){p=!0;break a}}}p=!1}else p=!0}}else p=!1;p?(l in v&&delete v[l],t[l]=h):v[l]=h}b=[t,v];c=b[0];b=b[1];for(var M in this.o)M in c?(this.l[M]||(this.o[M].style.display="",this.l[M]=!0),delete c[M]):M in b?(this.l[M]&&(this.o[M].style.display="none",delete this.l[M]),delete b[M]):(ff(this.o[M]),delete this.o[M],delete this.l[M]);for(M in c)d=document.createElement("LI"),d.innerHTML=c[M].a,this.O.appendChild(d),
this.o[M]=d,this.l[M]=!0;for(M in b)d=document.createElement("LI"),d.innerHTML=b[M].a,d.style.display="none",this.O.appendChild(d),this.o[M]=d;M=!Ca(this.l)||!Ca(a.logos);this.D!=M&&(this.element.style.display=M?"":"none",this.D=M);M&&Ca(this.l)?this.element.classList.add("ol-logo-only"):this.element.classList.remove("ol-logo-only");var oa;a=a.logos;M=this.T;for(oa in M)oa in a||(ff(M[oa]),delete M[oa]);for(var Wb in a)b=a[Wb],b instanceof HTMLElement&&(this.v.appendChild(b),M[Wb]=b),Wb in M||(oa=
new Image,oa.src=Wb,""===b?c=oa:(c=document.createElement("a"),c.href=b,c.appendChild(oa)),this.v.appendChild(c),M[Wb]=c);this.v.style.display=Ca(a)?"none":""}else this.D&&(this.element.style.display="none",this.D=!1)}k=kf.prototype;k.Wl=function(a){a.preventDefault();mf(this)};function mf(a){a.element.classList.toggle("ol-collapsed");a.f?ef(a.A,a.H):ef(a.H,a.A);a.f=!a.f}k.Vl=function(){return this.j};
k.Yl=function(a){this.j!==a&&(this.j=a,this.element.classList.toggle("ol-uncollapsible"),!a&&this.f&&mf(this))};k.Xl=function(a){this.j&&this.f!==a&&mf(this)};k.Ul=function(){return this.f};function nf(a){a=a?a:{};this.f=void 0!==a.className?a.className:"ol-full-screen";var b=void 0!==a.label?a.label:"\u2922";this.j="string"===typeof b?document.createTextNode(b):b;b=void 0!==a.labelActive?a.labelActive:"\u00d7";this.o="string"===typeof b?document.createTextNode(b):b;var c=a.tipLabel?a.tipLabel:"Toggle full-screen",b=document.createElement("button");b.className=this.f+"-"+of();b.setAttribute("type","button");b.title=c;b.appendChild(this.j);B(b,"click",this.H,this);c=document.createElement("div");
c.className=this.f+" ol-unselectable ol-control "+(pf()?"":"ol-unsupported");c.appendChild(b);jf.call(this,{element:c,target:a.target});this.A=void 0!==a.keys?a.keys:!1;this.l=a.source}w(nf,jf);
nf.prototype.H=function(a){a.preventDefault();pf()&&(a=this.a)&&(of()?document.exitFullscreen?document.exitFullscreen():document.msExitFullscreen?document.msExitFullscreen():document.mozCancelFullScreen?document.mozCancelFullScreen():document.webkitExitFullscreen&&document.webkitExitFullscreen():(a=this.l?"string"===typeof this.l?document.getElementById(this.l):this.l:a.xc(),this.A?a.mozRequestFullScreenWithKeys?a.mozRequestFullScreenWithKeys():a.webkitRequestFullscreen?a.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT):
qf(a):qf(a)))};nf.prototype.v=function(){var a=this.element.firstElementChild,b=this.a;of()?(a.className=this.f+"-true",ef(this.o,this.j)):(a.className=this.f+"-false",ef(this.j,this.o));b&&b.Wc()};nf.prototype.setMap=function(a){jf.prototype.setMap.call(this,a);a&&this.s.push(B(ja.document,rf(),this.v,this))};
function pf(){var a=document.body;return!!(a.webkitRequestFullscreen||a.mozRequestFullScreen&&document.mozFullScreenEnabled||a.msRequestFullscreen&&document.msFullscreenEnabled||a.requestFullscreen&&document.fullscreenEnabled)}function of(){return!!(document.webkitIsFullScreen||document.mozFullScreen||document.msFullscreenElement||document.fullscreenElement)}
function qf(a){a.requestFullscreen?a.requestFullscreen():a.msRequestFullscreen?a.msRequestFullscreen():a.mozRequestFullScreen?a.mozRequestFullScreen():a.webkitRequestFullscreen&&a.webkitRequestFullscreen()}var rf=function(){var a;return function(){if(!a){var b=document.body;b.webkitRequestFullscreen?a="webkitfullscreenchange":b.mozRequestFullScreen?a="mozfullscreenchange":b.msRequestFullscreen?a="MSFullscreenChange":b.requestFullscreen&&(a="fullscreenchange")}return a}}();function sf(a){a=a?a:{};var b=void 0!==a.className?a.className:"ol-rotate",c=void 0!==a.label?a.label:"\u21e7";this.f=null;"string"===typeof c?(this.f=document.createElement("span"),this.f.className="ol-compass",this.f.textContent=c):(this.f=c,this.f.classList.add("ol-compass"));var d=a.tipLabel?a.tipLabel:"Reset rotation",c=document.createElement("button");c.className=b+"-reset";c.setAttribute("type","button");c.title=d;c.appendChild(this.f);B(c,"click",sf.prototype.A,this);d=document.createElement("div");
d.className=b+" ol-unselectable ol-control";d.appendChild(c);b=a.render?a.render:uf;this.j=a.resetNorth?a.resetNorth:void 0;jf.call(this,{element:d,render:b,target:a.target});this.o=void 0!==a.duration?a.duration:250;this.l=void 0!==a.autoHide?a.autoHide:!0;this.v=void 0;this.l&&this.element.classList.add("ol-hidden")}w(sf,jf);
sf.prototype.A=function(a){a.preventDefault();if(void 0!==this.j)this.j();else{a=this.a;var b=a.aa();if(b){var c=b.Ma();void 0!==c&&(0<this.o&&(c%=2*Math.PI,c<-Math.PI&&(c+=2*Math.PI),c>Math.PI&&(c-=2*Math.PI),a.$a(Td({rotation:c,duration:this.o,easing:Md}))),b.ge(0))}}};
function uf(a){if(a=a.frameState){a=a.viewState.rotation;if(a!=this.v){var b="rotate("+a+"rad)";if(this.l){var c=this.element.classList.contains("ol-hidden");c||0!==a?c&&0!==a&&this.element.classList.remove("ol-hidden"):this.element.classList.add("ol-hidden")}this.f.style.msTransform=b;this.f.style.webkitTransform=b;this.f.style.transform=b}this.v=a}};function vf(a){a=a?a:{};var b=void 0!==a.className?a.className:"ol-zoom",c=void 0!==a.delta?a.delta:1,d=void 0!==a.zoomInLabel?a.zoomInLabel:"+",e=void 0!==a.zoomOutLabel?a.zoomOutLabel:"\u2212",f=void 0!==a.zoomInTipLabel?a.zoomInTipLabel:"Zoom in",g=void 0!==a.zoomOutTipLabel?a.zoomOutTipLabel:"Zoom out",h=document.createElement("button");h.className=b+"-in";h.setAttribute("type","button");h.title=f;h.appendChild("string"===typeof d?document.createTextNode(d):d);B(h,"click",vf.prototype.l.bind(this,
c));d=document.createElement("button");d.className=b+"-out";d.setAttribute("type","button");d.title=g;d.appendChild("string"===typeof e?document.createTextNode(e):e);B(d,"click",vf.prototype.l.bind(this,-c));c=document.createElement("div");c.className=b+" ol-unselectable ol-control";c.appendChild(h);c.appendChild(d);jf.call(this,{element:c,target:a.target});this.f=void 0!==a.duration?a.duration:250}w(vf,jf);
vf.prototype.l=function(a,b){b.preventDefault();var c=this.a,d=c.aa();if(d){var e=d.$();e&&(0<this.f&&c.$a(Ud({resolution:e,duration:this.f,easing:Md})),c=d.constrainResolution(e,a),d.Zb(c))}};function wf(a){a=a?a:{};var b=new re;(void 0!==a.zoom?a.zoom:1)&&b.push(new vf(a.zoomOptions));(void 0!==a.rotate?a.rotate:1)&&b.push(new sf(a.rotateOptions));(void 0!==a.attribution?a.attribution:1)&&b.push(new kf(a.attributionOptions));return b};function xf(a){a=a?a:{};var b=document.createElement("DIV");b.className=void 0!==a.className?a.className:"ol-mouse-position";jf.call(this,{element:b,render:a.render?a.render:yf,target:a.target});B(this,Za("projection"),this.Zl,this);a.coordinateFormat&&this.bi(a.coordinateFormat);a.projection&&this.fh(vc(a.projection));this.v=void 0!==a.undefinedHTML?a.undefinedHTML:"";this.o=b.innerHTML;this.j=this.l=this.f=null}w(xf,jf);
function yf(a){a=a.frameState;a?this.f!=a.viewState.projection&&(this.f=a.viewState.projection,this.l=null):this.f=null;zf(this,this.j)}k=xf.prototype;k.Zl=function(){this.l=null};k.Ag=function(){return this.get("coordinateFormat")};k.eh=function(){return this.get("projection")};k.Sk=function(a){this.j=this.a.Td(a);zf(this,this.j)};k.Tk=function(){zf(this,null);this.j=null};
k.setMap=function(a){jf.prototype.setMap.call(this,a);a&&(a=a.a,this.s.push(B(a,"mousemove",this.Sk,this),B(a,"mouseout",this.Tk,this)))};k.bi=function(a){this.set("coordinateFormat",a)};k.fh=function(a){this.set("projection",a)};function zf(a,b){var c=a.v;if(b&&a.f){if(!a.l){var d=a.eh();a.l=d?yc(a.f,d):Nc}if(d=a.a.Na(b))a.l(d,d),c=(c=a.Ag())?c(d):d.toString()}a.o&&c==a.o||(a.element.innerHTML=c,a.o=c)};function Af(a,b){var c=a;b&&(c=ea(a,b));"function"!=ba()||aa.Window&&aa.Window.prototype&&!Le("Edge")&&aa.Window.prototype.setImmediate==aa.setImmediate?(Bf||(Bf=Cf()),Bf(c)):aa.setImmediate(c)}var Bf;
function Cf(){var a=aa.MessageChannel;"undefined"===typeof a&&"undefined"!==typeof window&&window.postMessage&&window.addEventListener&&!Le("Presto")&&(a=function(){var a=document.createElement("IFRAME");a.style.display="none";a.src="";document.documentElement.appendChild(a);var b=a.contentWindow,a=b.document;a.open();a.write("");a.close();var c="callImmediate"+Math.random(),d="file:"==b.location.protocol?"*":b.location.protocol+"//"+b.location.host,a=ea(function(a){if(("*"==d||a.origin==d)&&a.data==
c)this.port1.onmessage()},this);b.addEventListener("message",a,!1);this.port1={};this.port2={postMessage:function(){b.postMessage(c,d)}}});if("undefined"!==typeof a&&!Le("Trident")&&!Le("MSIE")){var b=new a,c={},d=c;b.port1.onmessage=function(){if(void 0!==c.next){c=c.next;var a=c.qg;c.qg=null;a()}};return function(a){d.next={qg:a};d=d.next;b.port2.postMessage(0)}}return"undefined"!==typeof document&&"onreadystatechange"in document.createElement("SCRIPT")?function(a){var b=document.createElement("SCRIPT");
b.onreadystatechange=function(){b.onreadystatechange=null;b.parentNode.removeChild(b);b=null;a();a=null};document.documentElement.appendChild(b)}:function(a){aa.setTimeout(a,0)}};var Df=["experimental-webgl","webgl","webkit-3d","moz-webgl"];function Ef(a,b){var c,d,e=Df.length;for(d=0;d<e;++d)try{if(c=a.getContext(Df[d],b))return c}catch(f){}return null};var Ff,Gf="undefined"!==typeof navigator?navigator.userAgent.toLowerCase():"",Hf=-1!==Gf.indexOf("firefox"),If=-1!==Gf.indexOf("safari")&&-1===Gf.indexOf("chrom"),Jf=-1!==Gf.indexOf("macintosh"),Kf=ja.devicePixelRatio||1,Lf=!1,Mf=function(){if(!("HTMLCanvasElement"in ja))return!1;try{var a=$e();return a?(a.setLineDash&&(Lf=!0),!0):!1}catch(b){return!1}}(),Nf="DeviceOrientationEvent"in ja,Of="geolocation"in ja.navigator,Pf="ontouchstart"in ja,Qf="PointerEvent"in ja,Rf=!!ja.navigator.msPointerEnabled,
Sf=!1,Tf,Uf=[];if("WebGLRenderingContext"in ja)try{var Vf=Ef(document.createElement("CANVAS"),{failIfMajorPerformanceCaveat:!0});Vf&&(Sf=!0,Tf=Vf.getParameter(Vf.MAX_TEXTURE_SIZE),Uf=Vf.getSupportedExtensions())}catch(a){}Ff=Sf;ga=Uf;fa=Tf;function Wf(a,b){this.b=a;this.c=b};function Xf(a){Wf.call(this,a,{mousedown:this.ml,mousemove:this.nl,mouseup:this.ql,mouseover:this.pl,mouseout:this.ol});this.a=a.g;this.g=[]}w(Xf,Wf);function Yf(a,b){for(var c=a.g,d=b.clientX,e=b.clientY,f=0,g=c.length,h;f<g&&(h=c[f]);f++){var l=Math.abs(e-h[1]);if(25>=Math.abs(d-h[0])&&25>=l)return!0}return!1}function Zf(a){var b=$f(a,a),c=b.preventDefault;b.preventDefault=function(){a.preventDefault();c()};b.pointerId=1;b.isPrimary=!0;b.pointerType="mouse";return b}k=Xf.prototype;
k.ml=function(a){if(!Yf(this,a)){if((1).toString()in this.a){var b=Zf(a);ag(this.b,bg,b,a);delete this.a[(1).toString()]}b=Zf(a);this.a[(1).toString()]=a;ag(this.b,cg,b,a)}};k.nl=function(a){if(!Yf(this,a)){var b=Zf(a);ag(this.b,dg,b,a)}};k.ql=function(a){if(!Yf(this,a)){var b=this.a[(1).toString()];b&&b.button===a.button&&(b=Zf(a),ag(this.b,eg,b,a),delete this.a[(1).toString()])}};k.pl=function(a){if(!Yf(this,a)){var b=Zf(a);fg(this.b,b,a)}};
k.ol=function(a){if(!Yf(this,a)){var b=Zf(a);gg(this.b,b,a)}};function hg(a){Wf.call(this,a,{MSPointerDown:this.vl,MSPointerMove:this.wl,MSPointerUp:this.zl,MSPointerOut:this.xl,MSPointerOver:this.yl,MSPointerCancel:this.ul,MSGotPointerCapture:this.sl,MSLostPointerCapture:this.tl});this.a=a.g;this.g=["","unavailable","touch","pen","mouse"]}w(hg,Wf);function ig(a,b){var c=b;"number"===typeof b.pointerType&&(c=$f(b,b),c.pointerType=a.g[b.pointerType]);return c}k=hg.prototype;k.vl=function(a){this.a[a.pointerId.toString()]=a;var b=ig(this,a);ag(this.b,cg,b,a)};
k.wl=function(a){var b=ig(this,a);ag(this.b,dg,b,a)};k.zl=function(a){var b=ig(this,a);ag(this.b,eg,b,a);delete this.a[a.pointerId.toString()]};k.xl=function(a){var b=ig(this,a);gg(this.b,b,a)};k.yl=function(a){var b=ig(this,a);fg(this.b,b,a)};k.ul=function(a){var b=ig(this,a);ag(this.b,bg,b,a);delete this.a[a.pointerId.toString()]};k.tl=function(a){this.b.b(new jg("lostpointercapture",a,a))};k.sl=function(a){this.b.b(new jg("gotpointercapture",a,a))};function kg(a){Wf.call(this,a,{pointerdown:this.io,pointermove:this.jo,pointerup:this.mo,pointerout:this.ko,pointerover:this.lo,pointercancel:this.ho,gotpointercapture:this.Dk,lostpointercapture:this.ll})}w(kg,Wf);k=kg.prototype;k.io=function(a){lg(this.b,a)};k.jo=function(a){lg(this.b,a)};k.mo=function(a){lg(this.b,a)};k.ko=function(a){lg(this.b,a)};k.lo=function(a){lg(this.b,a)};k.ho=function(a){lg(this.b,a)};k.ll=function(a){lg(this.b,a)};k.Dk=function(a){lg(this.b,a)};function jg(a,b,c){Pa.call(this,a);this.b=b;a=c?c:{};this.buttons=mg(a);this.pressure=ng(a,this.buttons);this.bubbles="bubbles"in a?a.bubbles:!1;this.cancelable="cancelable"in a?a.cancelable:!1;this.view="view"in a?a.view:null;this.detail="detail"in a?a.detail:null;this.screenX="screenX"in a?a.screenX:0;this.screenY="screenY"in a?a.screenY:0;this.clientX="clientX"in a?a.clientX:0;this.clientY="clientY"in a?a.clientY:0;this.button="button"in a?a.button:0;this.relatedTarget="relatedTarget"in a?a.relatedTarget:
null;this.pointerId="pointerId"in a?a.pointerId:0;this.width="width"in a?a.width:0;this.height="height"in a?a.height:0;this.pointerType="pointerType"in a?a.pointerType:"";this.isPrimary="isPrimary"in a?a.isPrimary:!1;b.preventDefault&&(this.preventDefault=function(){b.preventDefault()})}w(jg,Pa);function mg(a){if(a.buttons||og)a=a.buttons;else switch(a.which){case 1:a=1;break;case 2:a=4;break;case 3:a=2;break;default:a=0}return a}
function ng(a,b){var c=0;a.pressure?c=a.pressure:c=b?.5:0;return c}var og=!1;try{og=1===(new MouseEvent("click",{buttons:1})).buttons}catch(a){};function pg(a,b){Wf.call(this,a,{touchstart:this.pp,touchmove:this.op,touchend:this.np,touchcancel:this.mp});this.a=a.g;this.l=b;this.g=void 0;this.i=0;this.f=void 0}w(pg,Wf);k=pg.prototype;k.$h=function(){this.i=0;this.f=void 0};
function qg(a,b,c){b=$f(b,c);b.pointerId=c.identifier+2;b.bubbles=!0;b.cancelable=!0;b.detail=a.i;b.button=0;b.buttons=1;b.width=c.webkitRadiusX||c.radiusX||0;b.height=c.webkitRadiusY||c.radiusY||0;b.pressure=c.webkitForce||c.force||.5;b.isPrimary=a.g===c.identifier;b.pointerType="touch";b.clientX=c.clientX;b.clientY=c.clientY;b.screenX=c.screenX;b.screenY=c.screenY;return b}
function rg(a,b,c){function d(){b.preventDefault()}var e=Array.prototype.slice.call(b.changedTouches),f=e.length,g,h;for(g=0;g<f;++g)h=qg(a,b,e[g]),h.preventDefault=d,c.call(a,b,h)}
k.pp=function(a){var b=a.touches,c=Object.keys(this.a),d=c.length;if(d>=b.length){var e=[],f,g,h;for(f=0;f<d;++f){g=c[f];h=this.a[g];var l;if(!(l=1==g))a:{l=b.length;for(var m,n=0;n<l;n++)if(m=b[n],m.identifier===g-2){l=!0;break a}l=!1}l||e.push(h.out)}for(f=0;f<e.length;++f)this.Se(a,e[f])}b=a.changedTouches[0];c=Object.keys(this.a).length;if(0===c||1===c&&(1).toString()in this.a)this.g=b.identifier,b=ja,void 0!==this.f&&b.clearTimeout(this.f);sg(this,a);this.i++;rg(this,a,this.bo)};
k.bo=function(a,b){this.a[b.pointerId]={target:b.target,out:b,Ih:b.target};var c=this.b;b.bubbles=!0;ag(c,tg,b,a);c=this.b;b.bubbles=!1;ag(c,ug,b,a);ag(this.b,cg,b,a)};k.op=function(a){a.preventDefault();rg(this,a,this.rl)};k.rl=function(a,b){var c=this.a[b.pointerId];if(c){var d=c.out,e=c.Ih;ag(this.b,dg,b,a);d&&e!==b.target&&(d.relatedTarget=b.target,b.relatedTarget=e,d.target=e,b.target?(gg(this.b,d,a),fg(this.b,b,a)):(b.target=e,b.relatedTarget=null,this.Se(a,b)));c.out=b;c.Ih=b.target}};
k.np=function(a){sg(this,a);rg(this,a,this.qp)};k.qp=function(a,b){ag(this.b,eg,b,a);this.b.out(b,a);var c=this.b;b.bubbles=!1;ag(c,vg,b,a);delete this.a[b.pointerId];b.isPrimary&&(this.g=void 0,this.f=ja.setTimeout(this.$h.bind(this),200))};k.mp=function(a){rg(this,a,this.Se)};k.Se=function(a,b){ag(this.b,bg,b,a);this.b.out(b,a);var c=this.b;b.bubbles=!1;ag(c,vg,b,a);delete this.a[b.pointerId];b.isPrimary&&(this.g=void 0,this.f=ja.setTimeout(this.$h.bind(this),200))};
function sg(a,b){var c=a.l.g,d=b.changedTouches[0];if(a.g===d.identifier){var e=[d.clientX,d.clientY];c.push(e);ja.setTimeout(function(){fb(c,e)},2500)}};function wg(a){Sa.call(this);this.i=a;this.g={};this.c={};this.a=[];Qf?xg(this,new kg(this)):Rf?xg(this,new hg(this)):(a=new Xf(this),xg(this,a),Pf&&xg(this,new pg(this,a)));a=this.a.length;for(var b,c=0;c<a;c++)b=this.a[c],yg(this,Object.keys(b.c))}w(wg,Sa);function xg(a,b){var c=Object.keys(b.c);c&&(c.forEach(function(a){var c=b.c[a];c&&(this.c[a]=c.bind(b))},a),a.a.push(b))}wg.prototype.f=function(a){var b=this.c[a.type];b&&b(a)};
function yg(a,b){b.forEach(function(a){B(this.i,a,this.f,this)},a)}function zg(a,b){b.forEach(function(a){Ka(this.i,a,this.f,this)},a)}function $f(a,b){for(var c={},d,e=0,f=Ag.length;e<f;e++)d=Ag[e][0],c[d]=a[d]||b[d]||Ag[e][1];return c}wg.prototype.out=function(a,b){a.bubbles=!0;ag(this,Bg,a,b)};function gg(a,b,c){a.out(b,c);var d=b.target,e=b.relatedTarget;d&&e&&d.contains(e)||(b.bubbles=!1,ag(a,vg,b,c))}
function fg(a,b,c){b.bubbles=!0;ag(a,tg,b,c);var d=b.target,e=b.relatedTarget;d&&e&&d.contains(e)||(b.bubbles=!1,ag(a,ug,b,c))}function ag(a,b,c,d){a.b(new jg(b,d,c))}function lg(a,b){a.b(new jg(b.type,b,b))}wg.prototype.ma=function(){for(var a=this.a.length,b,c=0;c<a;c++)b=this.a[c],zg(this,Object.keys(b.c));Sa.prototype.ma.call(this)};
var dg="pointermove",cg="pointerdown",eg="pointerup",tg="pointerover",Bg="pointerout",ug="pointerenter",vg="pointerleave",bg="pointercancel",Ag=[["bubbles",!1],["cancelable",!1],["view",null],["detail",null],["screenX",0],["screenY",0],["clientX",0],["clientY",0],["ctrlKey",!1],["altKey",!1],["shiftKey",!1],["metaKey",!1],["button",0],["relatedTarget",null],["buttons",0],["pointerId",0],["width",0],["height",0],["pressure",0],["tiltX",0],["tiltY",0],["pointerType",""],["hwTimestamp",0],["isPrimary",
!1],["type",""],["target",null],["currentTarget",null],["which",0]];function Cg(a,b,c,d,e){hf.call(this,a,b,e);this.originalEvent=c;this.pixel=b.Td(c);this.coordinate=b.Na(this.pixel);this.dragging=void 0!==d?d:!1}w(Cg,hf);Cg.prototype.preventDefault=function(){hf.prototype.preventDefault.call(this);this.originalEvent.preventDefault()};Cg.prototype.stopPropagation=function(){hf.prototype.stopPropagation.call(this);this.originalEvent.stopPropagation()};function Dg(a,b,c,d,e){Cg.call(this,a,b,c.b,d,e);this.b=c}w(Dg,Cg);
function Eg(a){Sa.call(this);this.f=a;this.l=0;this.j=!1;this.c=[];this.g=null;a=this.f.a;this.U=0;this.v={};this.i=new wg(a);this.a=null;this.o=B(this.i,cg,this.Vk,this);this.s=B(this.i,dg,this.Ko,this)}w(Eg,Sa);function Fg(a,b){var c;c=new Dg(Gg,a.f,b);a.b(c);0!==a.l?(ja.clearTimeout(a.l),a.l=0,c=new Dg(Hg,a.f,b),a.b(c)):a.l=ja.setTimeout(function(){this.l=0;var a=new Dg(Ig,this.f,b);this.b(a)}.bind(a),250)}
function Jg(a,b){b.type==Kg||b.type==Lg?delete a.v[b.pointerId]:b.type==Mg&&(a.v[b.pointerId]=!0);a.U=Object.keys(a.v).length}k=Eg.prototype;k.Og=function(a){Jg(this,a);var b=new Dg(Kg,this.f,a);this.b(b);!this.j&&0===a.button&&Fg(this,this.g);0===this.U&&(this.c.forEach(Ea),this.c.length=0,this.j=!1,this.g=null,Oa(this.a),this.a=null)};
k.Vk=function(a){Jg(this,a);var b=new Dg(Mg,this.f,a);this.b(b);this.g=a;0===this.c.length&&(this.a=new wg(document),this.c.push(B(this.a,Ng,this.Nl,this),B(this.a,Kg,this.Og,this),B(this.i,Lg,this.Og,this)))};k.Nl=function(a){if(a.clientX!=this.g.clientX||a.clientY!=this.g.clientY){this.j=!0;var b=new Dg(Og,this.f,a,this.j);this.b(b)}a.preventDefault()};k.Ko=function(a){this.b(new Dg(a.type,this.f,a,!(!this.g||a.clientX==this.g.clientX&&a.clientY==this.g.clientY)))};
k.ma=function(){this.s&&(Ea(this.s),this.s=null);this.o&&(Ea(this.o),this.o=null);this.c.forEach(Ea);this.c.length=0;this.a&&(Oa(this.a),this.a=null);this.i&&(Oa(this.i),this.i=null);Sa.prototype.ma.call(this)};var Ig="singleclick",Gg="click",Hg="dblclick",Og="pointerdrag",Ng="pointermove",Mg="pointerdown",Kg="pointerup",Lg="pointercancel",Pg={Jp:Ig,yp:Gg,zp:Hg,Cp:Og,Fp:Ng,Bp:Mg,Ip:Kg,Hp:"pointerover",Gp:"pointerout",Dp:"pointerenter",Ep:"pointerleave",Ap:Lg};function Qg(a){Xa.call(this);var b=za({},a);b.opacity=void 0!==a.opacity?a.opacity:1;b.visible=void 0!==a.visible?a.visible:!0;b.zIndex=void 0!==a.zIndex?a.zIndex:0;b.maxResolution=void 0!==a.maxResolution?a.maxResolution:Infinity;b.minResolution=void 0!==a.minResolution?a.minResolution:0;this.G(b)}w(Qg,Xa);
function Rg(a){var b=a.Tb(),c=a.hf(),d=a.Bb(),e=a.C(),f=a.Ub(),g=a.Rb(),h=a.Sb();return{layer:a,opacity:ma(b,0,1),O:c,visible:d,Oc:!0,extent:e,zIndex:f,maxResolution:g,minResolution:Math.max(h,0)}}k=Qg.prototype;k.C=function(){return this.get("extent")};k.Rb=function(){return this.get("maxResolution")};k.Sb=function(){return this.get("minResolution")};k.Tb=function(){return this.get("opacity")};k.Bb=function(){return this.get("visible")};k.Ub=function(){return this.get("zIndex")};
k.ic=function(a){this.set("extent",a)};k.oc=function(a){this.set("maxResolution",a)};k.pc=function(a){this.set("minResolution",a)};k.jc=function(a){this.set("opacity",a)};k.kc=function(a){this.set("visible",a)};k.lc=function(a){this.set("zIndex",a)};function Sg(a,b,c,d,e){Pa.call(this,a);this.vectorContext=b;this.frameState=c;this.context=d;this.glContext=e}w(Sg,Pa);function Tg(a){Xa.call(this);this.f=vc(a.projection);this.l=Ug(a.attributions);this.O=a.logo;this.Ha=void 0!==a.state?a.state:"ready";this.D=void 0!==a.wrapX?a.wrapX:!1}w(Tg,Xa);function Ug(a){if("string"===typeof a)return[new qe({html:a})];if(a instanceof qe)return[a];if(Array.isArray(a)){for(var b=a.length,c=Array(b),d=0;d<b;d++){var e=a[d];c[d]="string"===typeof e?new qe({html:e}):e}return c}return null}k=Tg.prototype;k.ta=ha;k.xa=function(){return this.l};k.wa=function(){return this.O};k.ya=function(){return this.f};
k.V=function(){return this.Ha};k.va=function(){this.u()};k.ra=function(a){this.l=Ug(a);this.u()};function Vg(a,b){a.Ha=b;a.u()};function Wg(a){var b=za({},a);delete b.source;Qg.call(this,b);this.v=this.o=this.j=null;a.map&&this.setMap(a.map);B(this,Za("source"),this.al,this);this.Dc(a.source?a.source:null)}w(Wg,Qg);function Xg(a,b){return a.visible&&b>=a.minResolution&&b<a.maxResolution}k=Wg.prototype;k.ff=function(a){a=a?a:[];a.push(Rg(this));return a};k.ga=function(){return this.get("source")||null};k.hf=function(){var a=this.ga();return a?a.V():"undefined"};k.Gm=function(){this.u()};
k.al=function(){this.v&&(Ea(this.v),this.v=null);var a=this.ga();a&&(this.v=B(a,"change",this.Gm,this));this.u()};k.setMap=function(a){this.j&&(Ea(this.j),this.j=null);a||this.u();this.o&&(Ea(this.o),this.o=null);a&&(this.j=B(a,"precompose",function(a){var c=Rg(this);c.Oc=!1;c.zIndex=Infinity;a.frameState.layerStatesArray.push(c);a.frameState.layerStates[x(this)]=c},this),this.o=B(this,"change",a.render,a),this.u())};k.Dc=function(a){this.set("source",a)};function Yg(){this.b={};this.a=0}Yg.prototype.clear=function(){this.b={};this.a=0};Yg.prototype.get=function(a,b,c){a=b+":"+a+":"+(c?Ce(c):"null");return a in this.b?this.b[a]:null};Yg.prototype.set=function(a,b,c,d){this.b[b+":"+a+":"+(c?Ce(c):"null")]=d;++this.a};var Zg=new Yg;var $g=Array(6);function ah(){return[1,0,0,1,0,0]}function bh(a){return ch(a,1,0,0,1,0,0)}function dh(a,b){var c=a[0],d=a[1],e=a[2],f=a[3],g=a[4],h=a[5],l=b[0],m=b[1],n=b[2],p=b[3],q=b[4],t=b[5];a[0]=c*l+e*m;a[1]=d*l+f*m;a[2]=c*n+e*p;a[3]=d*n+f*p;a[4]=c*q+e*t+g;a[5]=d*q+f*t+h;return a}function ch(a,b,c,d,e,f,g){a[0]=b;a[1]=c;a[2]=d;a[3]=e;a[4]=f;a[5]=g;return a}function eh(a,b){a[0]=b[0];a[1]=b[1];a[2]=b[2];a[3]=b[3];a[4]=b[4];a[5]=b[5];return a}
function fh(a,b){var c=b[0],d=b[1];b[0]=a[0]*c+a[2]*d+a[4];b[1]=a[1]*c+a[3]*d+a[5];return b}function gh(a,b){var c=Math.cos(b),d=Math.sin(b);dh(a,ch($g,c,d,-d,c,0,0))}function hh(a,b,c){return dh(a,ch($g,b,0,0,c,0,0))}function ih(a,b,c){dh(a,ch($g,1,0,0,1,b,c))}function jh(a,b,c,d,e,f,g,h){var l=Math.sin(f);f=Math.cos(f);a[0]=d*f;a[1]=e*l;a[2]=-d*l;a[3]=e*f;a[4]=g*d*f-h*d*l+b;a[5]=g*e*l+h*e*f+c;return a}
function kh(a){var b=a[0]*a[3]-a[1]*a[2];la(0!==b,32);var c=a[0],d=a[1],e=a[2],f=a[3],g=a[4],h=a[5];a[0]=f/b;a[1]=-d/b;a[2]=-e/b;a[3]=c/b;a[4]=(e*h-f*g)/b;a[5]=-(c*h-d*g)/b;return a};function lh(a,b){this.i=b;this.g={};this.s={}}w(lh,Na);function mh(a){var b=a.viewState,c=a.coordinateToPixelTransform,d=a.pixelToCoordinateTransform;jh(c,a.size[0]/2,a.size[1]/2,1/b.resolution,-1/b.resolution,-b.rotation,-b.center[0],-b.center[1]);kh(eh(d,c))}k=lh.prototype;k.ma=function(){for(var a in this.g)Oa(this.g[a])};function nh(){if(32<Zg.a){var a=0,b,c;for(b in Zg.b)c=Zg.b[b],0!==(a++&3)||Ta(c)||(delete Zg.b[b],--Zg.a)}}
k.ta=function(a,b,c,d,e,f){function g(a,e){var f=x(a).toString(),g=b.layerStates[x(e)].Oc;if(!(f in b.skippedFeatureUids)||g)return c.call(d,a,g?e:null)}var h,l=b.viewState,m=l.resolution,n=l.projection,l=a;if(n.a){var n=n.C(),p=ec(n),q=a[0];if(q<n[0]||q>n[2])l=[q+p*Math.ceil((n[0]-q)/p),a[1]]}n=b.layerStatesArray;for(p=n.length-1;0<=p;--p){var t=n[p],q=t.layer;if(Xg(t,m)&&e.call(f,q)&&(t=oh(this,q),q.ga()&&(h=t.ta(q.ga().D?l:a,b,g,d)),h))return h}};
k.oh=function(a,b,c,d,e,f){var g,h=b.viewState.resolution,l=b.layerStatesArray,m;for(m=l.length-1;0<=m;--m){g=l[m];var n=g.layer;if(Xg(g,h)&&e.call(f,n)&&(g=oh(this,n).Ac(a,b,c,d)))return g}};k.ph=function(a,b,c,d){return void 0!==this.ta(a,b,nc,this,c,d)};function oh(a,b){var c=x(b).toString();if(c in a.g)return a.g[c];var d=a.Ve(b);a.g[c]=d;a.s[c]=B(d,"change",a.Mk,a);return d}k.Mk=function(){this.i.render()};k.Be=ha;
k.Qo=function(a,b){for(var c in this.g)if(!(b&&c in b.layerStates)){var d=c,e=this.g[d];delete this.g[d];Ea(this.s[d]);delete this.s[d];Oa(e)}};function ph(a,b){for(var c in a.g)if(!(c in b.layerStates)){b.postRenderFunctions.push(a.Qo.bind(a));break}}function jb(a,b){return a.zIndex-b.zIndex};function qh(a,b){Sa.call(this);this.oa=a;this.state=b;this.a=null;this.key=""}w(qh,Sa);function rh(a){a.b("change")}qh.prototype.Ya=function(){return this.key+"/"+this.oa};qh.prototype.i=function(){return this.oa};qh.prototype.V=function(){return this.state};function sh(a,b){this.o=a;this.f=b;this.b=[];this.g=[];this.a={}}sh.prototype.clear=function(){this.b.length=0;this.g.length=0;Aa(this.a)};function th(a){var b=a.b,c=a.g,d=b[0];1==b.length?(b.length=0,c.length=0):(b[0]=b.pop(),c[0]=c.pop(),uh(a,0));b=a.f(d);delete a.a[b];return d}sh.prototype.c=function(a){la(!(this.f(a)in this.a),31);var b=this.o(a);return Infinity!=b?(this.b.push(a),this.g.push(b),this.a[this.f(a)]=!0,vh(this,0,this.b.length-1),!0):!1};
function uh(a,b){for(var c=a.b,d=a.g,e=c.length,f=c[b],g=d[b],h=b;b<e>>1;){var l=2*b+1,m=2*b+2,l=m<e&&d[m]<d[l]?m:l;c[b]=c[l];d[b]=d[l];b=l}c[b]=f;d[b]=g;vh(a,h,b)}function vh(a,b,c){var d=a.b;a=a.g;for(var e=d[c],f=a[c];c>b;){var g=c-1>>1;if(a[g]>f)d[c]=d[g],a[c]=a[g],c=g;else break}d[c]=e;a[c]=f}function wh(a){var b=a.o,c=a.b,d=a.g,e=0,f=c.length,g,h,l;for(h=0;h<f;++h)g=c[h],l=b(g),Infinity==l?delete a.a[a.f(g)]:(d[e]=l,c[e++]=g);c.length=e;d.length=e;for(b=(a.b.length>>1)-1;0<=b;b--)uh(a,b)};function xh(a,b){sh.call(this,function(b){return a.apply(null,b)},function(a){return a[0].Ya()});this.s=b;this.l=0;this.i={}}w(xh,sh);xh.prototype.c=function(a){var b=sh.prototype.c.call(this,a);b&&B(a[0],"change",this.j,this);return b};xh.prototype.j=function(a){a=a.target;var b=a.V();if(2===b||3===b||4===b||5===b)Ka(a,"change",this.j,this),a=a.Ya(),a in this.i&&(delete this.i[a],--this.l),this.s()};
function yh(a,b,c){for(var d=0,e,f;a.l<b&&d<c&&0<a.b.length;)e=th(a)[0],f=e.Ya(),0!==e.V()||f in a.i||(a.i[f]=!0,++a.l,++d,e.load())};function zh(a,b,c){this.f=a;this.g=b;this.i=c;this.b=[];this.a=this.c=0}function Ah(a,b){var c=a.f,d=a.a,e=a.g-d,f=Math.log(a.g/a.a)/a.f;return Sd({source:b,duration:f,easing:function(a){return d*(Math.exp(c*a*f)-1)/e}})};function Bh(a){Xa.call(this);this.v=null;this.i(!0);this.handleEvent=a.handleEvent}w(Bh,Xa);Bh.prototype.f=function(){return this.get("active")};Bh.prototype.l=function(){return this.v};Bh.prototype.i=function(a){this.set("active",a)};Bh.prototype.setMap=function(a){this.v=a};function Ch(a,b,c,d,e){if(void 0!==c){var f=b.Ma(),g=b.bb();void 0!==f&&g&&e&&0<e&&(a.$a(Td({rotation:f,duration:e,easing:Md})),d&&a.$a(Sd({source:g,duration:e,easing:Md})));b.rotate(c,d)}}
function Dh(a,b,c,d,e){var f=b.$();c=b.constrainResolution(f,c,0);Eh(a,b,c,d,e)}function Eh(a,b,c,d,e){if(c){var f=b.$(),g=b.bb();void 0!==f&&g&&c!==f&&e&&0<e&&(a.$a(Ud({resolution:f,duration:e,easing:Md})),d&&a.$a(Sd({source:g,duration:e,easing:Md})));if(d){var h;a=b.bb();e=b.$();void 0!==a&&void 0!==e&&(h=[d[0]-c*(d[0]-a[0])/e,d[1]-c*(d[1]-a[1])/e]);b.ob(h)}b.Zb(c)}};function Fh(a){a=a?a:{};this.a=a.delta?a.delta:1;Bh.call(this,{handleEvent:Gh});this.c=void 0!==a.duration?a.duration:250}w(Fh,Bh);function Gh(a){var b=!1,c=a.originalEvent;if(a.type==Hg){var b=a.map,d=a.coordinate,c=c.shiftKey?-this.a:this.a,e=b.aa();Dh(b,e,c,d,this.c);a.preventDefault();b=!0}return!b};function Hh(a){a=a.originalEvent;return a.altKey&&!(a.metaKey||a.ctrlKey)&&a.shiftKey}function Ih(a){a=a.originalEvent;return 0==a.button&&!(Qe&&Jf&&a.ctrlKey)}function Jh(a){return"pointermove"==a.type}function Kh(a){return a.type==Ig}function Lh(a){a=a.originalEvent;return!a.altKey&&!(a.metaKey||a.ctrlKey)&&!a.shiftKey}function Mh(a){a=a.originalEvent;return!a.altKey&&!(a.metaKey||a.ctrlKey)&&a.shiftKey}
function Nh(a){a=a.originalEvent.target.tagName;return"INPUT"!==a&&"SELECT"!==a&&"TEXTAREA"!==a}function Oh(a){la(a.b,56);return"mouse"==a.b.pointerType}function Ph(a){a=a.b;return a.isPrimary&&0===a.button};function Qh(a){a=a?a:{};Bh.call(this,{handleEvent:a.handleEvent?a.handleEvent:Rh});this.Ne=a.handleDownEvent?a.handleDownEvent:oc;this.Ke=a.handleDragEvent?a.handleDragEvent:ha;this.pj=a.handleMoveEvent?a.handleMoveEvent:ha;this.xj=a.handleUpEvent?a.handleUpEvent:oc;this.H=!1;this.ha={};this.j=[]}w(Qh,Bh);function Sh(a){for(var b=a.length,c=0,d=0,e=0;e<b;e++)c+=a[e].clientX,d+=a[e].clientY;return[c/b,d/b]}
function Rh(a){if(!(a instanceof Dg))return!0;var b=!1,c=a.type;if(c===Mg||c===Og||c===Kg)c=a.b,a.type==Kg?delete this.ha[c.pointerId]:a.type==Mg?this.ha[c.pointerId]=c:c.pointerId in this.ha&&(this.ha[c.pointerId]=c),this.j=Ba(this.ha);this.H&&(a.type==Og?this.Ke(a):a.type==Kg&&(this.H=this.xj(a)));a.type==Mg?(this.H=a=this.Ne(a),b=this.Ec(a)):a.type==Ng&&this.pj(a);return!b}Qh.prototype.Ec=function(a){return a};function Th(a){Qh.call(this,{handleDownEvent:Uh,handleDragEvent:Vh,handleUpEvent:Wh});a=a?a:{};this.a=a.kinetic;this.c=this.o=null;this.A=a.condition?a.condition:Lh;this.s=!1}w(Th,Qh);function Vh(a){var b=Sh(this.j);this.a&&this.a.b.push(b[0],b[1],Date.now());if(this.c){var c=this.c[0]-b[0],d=b[1]-this.c[1];a=a.map.aa();var e=a.V(),d=c=[c,d],f=e.resolution;d[0]*=f;d[1]*=f;zb(c,e.rotation);ub(c,e.center);c=a.Od(c);a.ob(c)}this.c=b}
function Wh(a){var b=a.map;a=b.aa();if(0===this.j.length){var c;if(c=!this.s&&this.a)if(c=this.a,6>c.b.length)c=!1;else{var d=Date.now()-c.i,e=c.b.length-3;if(c.b[e+2]<d)c=!1;else{for(var f=e-3;0<f&&c.b[f+2]>d;)f-=3;var d=c.b[e+2]-c.b[f+2],g=c.b[e]-c.b[f],e=c.b[e+1]-c.b[f+1];c.c=Math.atan2(e,g);c.a=Math.sqrt(g*g+e*e)/d;c=c.a>c.g}}c?(c=this.a,c=(c.g-c.a)/c.f,e=this.a.c,f=a.bb(),this.o=Ah(this.a,f),b.$a(this.o),f=b.Ga(f),b=b.Na([f[0]-c*Math.cos(e),f[1]-c*Math.sin(e)]),b=a.Od(b),a.ob(b)):b.render();
Kd(a,-1);return!1}this.c=null;return!0}function Uh(a){if(0<this.j.length&&this.A(a)){var b=a.map,c=b.aa();this.c=null;this.H||Kd(c,1);this.o&&fb(b.O,this.o)&&(c.ob(a.frameState.viewState.center),this.o=null);this.a&&(a=this.a,a.b.length=0,a.c=0,a.a=0);this.s=1<this.j.length;return!0}return!1}Th.prototype.Ec=oc;function Xh(a){a=a?a:{};Qh.call(this,{handleDownEvent:Yh,handleDragEvent:Zh,handleUpEvent:$h});this.c=a.condition?a.condition:Hh;this.a=void 0;this.o=void 0!==a.duration?a.duration:250}w(Xh,Qh);function Zh(a){if(Oh(a)){var b=a.map,c=b.kb();a=a.pixel;c=Math.atan2(c[1]/2-a[1],a[0]-c[0]/2);if(void 0!==this.a){a=c-this.a;var d=b.aa(),e=d.Ma();Ch(b,d,e-a)}this.a=c}}
function $h(a){if(!Oh(a))return!0;a=a.map;var b=a.aa();Kd(b,-1);var c=b.Ma(),d=this.o,c=b.constrainRotation(c,0);Ch(a,b,c,void 0,d);return!1}function Yh(a){return Oh(a)&&Ih(a)&&this.c(a)?(Kd(a.map.aa(),1),this.a=void 0,!0):!1}Xh.prototype.Ec=oc;function ai(a){this.f=null;this.a=document.createElement("div");this.a.style.position="absolute";this.a.className="ol-box "+a;this.g=this.c=this.b=null}w(ai,Na);ai.prototype.ma=function(){this.setMap(null)};function bi(a){var b=a.c,c=a.g;a=a.a.style;a.left=Math.min(b[0],c[0])+"px";a.top=Math.min(b[1],c[1])+"px";a.width=Math.abs(c[0]-b[0])+"px";a.height=Math.abs(c[1]-b[1])+"px"}
ai.prototype.setMap=function(a){if(this.b){this.b.A.removeChild(this.a);var b=this.a.style;b.left=b.top=b.width=b.height="inherit"}(this.b=a)&&this.b.A.appendChild(this.a)};function ci(a){var b=a.c,c=a.g,b=[b,[b[0],c[1]],c,[c[0],b[1]]].map(a.b.Na,a.b);b[4]=b[0].slice();a.f?a.f.sa([b]):a.f=new D([b])}ai.prototype.Y=function(){return this.f};function di(a,b,c){Pa.call(this,a);this.coordinate=b;this.mapBrowserEvent=c}w(di,Pa);function ei(a){Qh.call(this,{handleDownEvent:fi,handleDragEvent:gi,handleUpEvent:hi});a=a?a:{};this.a=new ai(a.className||"ol-dragbox");this.c=null;this.D=a.condition?a.condition:nc;this.A=a.boxEndCondition?a.boxEndCondition:ii}w(ei,Qh);function ii(a,b,c){a=c[0]-b[0];b=c[1]-b[1];return 64<=a*a+b*b}
function gi(a){if(Oh(a)){var b=this.a,c=a.pixel;b.c=this.c;b.g=c;ci(b);bi(b);this.b(new di("boxdrag",a.coordinate,a))}}ei.prototype.Y=function(){return this.a.Y()};ei.prototype.s=ha;function hi(a){if(!Oh(a))return!0;this.a.setMap(null);this.A(a,this.c,a.pixel)&&(this.s(a),this.b(new di("boxend",a.coordinate,a)));return!1}
function fi(a){if(Oh(a)&&Ih(a)&&this.D(a)){this.c=a.pixel;this.a.setMap(a.map);var b=this.a,c=this.c;b.c=this.c;b.g=c;ci(b);bi(b);this.b(new di("boxstart",a.coordinate,a));return!0}return!1};function ji(a){a=a?a:{};var b=a.condition?a.condition:Mh;this.o=void 0!==a.duration?a.duration:200;this.O=void 0!==a.out?a.out:!1;ei.call(this,{condition:b,className:a.className||"ol-dragzoom"})}w(ji,ei);
ji.prototype.s=function(){var a=this.v,b=a.aa(),c=a.kb(),d=this.Y().C();if(this.O){var e=b.Ic(c),d=[a.Ga(Zb(d)),a.Ga(ac(d))],f=Qb(Infinity,Infinity,-Infinity,-Infinity,void 0),g,h;g=0;for(h=d.length;g<h;++g)Fb(f,d[g]);lc(e,1/Gd(f,c));d=e}c=b.constrainResolution(Gd(d,c));e=b.$();f=b.bb();a.$a(Ud({resolution:e,duration:this.o,easing:Md}));a.$a(Sd({source:f,duration:this.o,easing:Md}));b.ob(gc(d));b.Zb(c)};function ki(a){Bh.call(this,{handleEvent:li});a=a||{};this.a=function(a){return Lh(a)&&Nh(a)};this.c=void 0!==a.condition?a.condition:this.a;this.j=void 0!==a.duration?a.duration:100;this.o=void 0!==a.pixelDelta?a.pixelDelta:128}w(ki,Bh);
function li(a){var b=!1;if("keydown"==a.type){var c=a.originalEvent.keyCode;if(this.c(a)&&(40==c||37==c||39==c||38==c)){var d=a.map,b=d.aa(),e=b.$()*this.o,f=0,g=0;40==c?g=-e:37==c?f=-e:39==c?f=e:g=e;c=[f,g];zb(c,b.Ma());e=this.j;if(f=b.bb())e&&0<e&&d.$a(Sd({source:f,duration:e,easing:Qd})),d=b.Od([f[0]+c[0],f[1]+c[1]]),b.ob(d);a.preventDefault();b=!0}}return!b};function mi(a){Bh.call(this,{handleEvent:ni});a=a?a:{};this.c=a.condition?a.condition:Nh;this.a=a.delta?a.delta:1;this.j=void 0!==a.duration?a.duration:100}w(mi,Bh);function ni(a){var b=!1;if("keydown"==a.type||"keypress"==a.type){var c=a.originalEvent.charCode;if(this.c(a)&&(43==c||45==c)){var b=a.map,c=43==c?this.a:-this.a,d=b.aa();Dh(b,d,c,void 0,this.j);a.preventDefault();b=!0}}return!b};function oi(a){Bh.call(this,{handleEvent:pi});a=a||{};this.c=0;this.H=void 0!==a.duration?a.duration:250;this.s=void 0!==a.useAnchor?a.useAnchor:!0;this.a=null;this.o=this.j=void 0}w(oi,Bh);
function pi(a){var b=!1;if("wheel"==a.type||"mousewheel"==a.type){var b=a.map,c=a.originalEvent;this.s&&(this.a=a.coordinate);var d;"wheel"==a.type?(d=c.deltaY,Hf&&c.deltaMode===ja.WheelEvent.DOM_DELTA_PIXEL&&(d/=Kf),c.deltaMode===ja.WheelEvent.DOM_DELTA_LINE&&(d*=40)):"mousewheel"==a.type&&(d=-c.wheelDeltaY,If&&(d/=3));this.c+=d;void 0===this.j&&(this.j=Date.now());d=Math.max(80-(Date.now()-this.j),0);ja.clearTimeout(this.o);this.o=ja.setTimeout(this.A.bind(this,b),d);a.preventDefault();b=!0}return!b}
oi.prototype.A=function(a){var b=ma(this.c,-1,1),c=a.aa();Dh(a,c,-b,this.a,this.H);this.c=0;this.a=null;this.o=this.j=void 0};oi.prototype.D=function(a){this.s=a;a||(this.a=null)};function qi(a){Qh.call(this,{handleDownEvent:ri,handleDragEvent:si,handleUpEvent:ti});a=a||{};this.c=null;this.o=void 0;this.a=!1;this.s=0;this.D=void 0!==a.threshold?a.threshold:.3;this.A=void 0!==a.duration?a.duration:250}w(qi,Qh);
function si(a){var b=0,c=this.j[0],d=this.j[1],c=Math.atan2(d.clientY-c.clientY,d.clientX-c.clientX);void 0!==this.o&&(b=c-this.o,this.s+=b,!this.a&&Math.abs(this.s)>this.D&&(this.a=!0));this.o=c;a=a.map;c=a.a.getBoundingClientRect();d=Sh(this.j);d[0]-=c.left;d[1]-=c.top;this.c=a.Na(d);this.a&&(c=a.aa(),d=c.Ma(),a.render(),Ch(a,c,d+b,this.c))}
function ti(a){if(2>this.j.length){a=a.map;var b=a.aa();Kd(b,-1);if(this.a){var c=b.Ma(),d=this.c,e=this.A,c=b.constrainRotation(c,0);Ch(a,b,c,d,e)}return!1}return!0}function ri(a){return 2<=this.j.length?(a=a.map,this.c=null,this.o=void 0,this.a=!1,this.s=0,this.H||Kd(a.aa(),1),a.render(),!0):!1}qi.prototype.Ec=oc;function ui(a){Qh.call(this,{handleDownEvent:vi,handleDragEvent:wi,handleUpEvent:xi});a=a?a:{};this.c=null;this.s=void 0!==a.duration?a.duration:400;this.a=void 0;this.o=1}w(ui,Qh);function wi(a){var b=1,c=this.j[0],d=this.j[1],e=c.clientX-d.clientX,c=c.clientY-d.clientY,e=Math.sqrt(e*e+c*c);void 0!==this.a&&(b=this.a/e);this.a=e;1!=b&&(this.o=b);a=a.map;var e=a.aa(),c=e.$(),d=a.a.getBoundingClientRect(),f=Sh(this.j);f[0]-=d.left;f[1]-=d.top;this.c=a.Na(f);a.render();Eh(a,e,c*b,this.c)}
function xi(a){if(2>this.j.length){a=a.map;var b=a.aa();Kd(b,-1);var c=b.$(),d=this.c,e=this.s,c=b.constrainResolution(c,0,this.o-1);Eh(a,b,c,d,e);return!1}return!0}function vi(a){return 2<=this.j.length?(a=a.map,this.c=null,this.a=void 0,this.o=1,this.H||Kd(a.aa(),1),a.render(),!0):!1}ui.prototype.Ec=oc;function yi(a){a=a?a:{};var b=new re,c=new zh(-.005,.05,100);(void 0!==a.altShiftDragRotate?a.altShiftDragRotate:1)&&b.push(new Xh);(void 0!==a.doubleClickZoom?a.doubleClickZoom:1)&&b.push(new Fh({delta:a.zoomDelta,duration:a.zoomDuration}));(void 0!==a.dragPan?a.dragPan:1)&&b.push(new Th({kinetic:c}));(void 0!==a.pinchRotate?a.pinchRotate:1)&&b.push(new qi);(void 0!==a.pinchZoom?a.pinchZoom:1)&&b.push(new ui({duration:a.zoomDuration}));if(void 0!==a.keyboard?a.keyboard:1)b.push(new ki),b.push(new mi({delta:a.zoomDelta,
duration:a.zoomDuration}));(void 0!==a.mouseWheelZoom?a.mouseWheelZoom:1)&&b.push(new oi({duration:a.zoomDuration}));(void 0!==a.shiftDragZoom?a.shiftDragZoom:1)&&b.push(new ji({duration:a.zoomDuration}));return b};function zi(a){var b=a||{};a=za({},b);delete a.layers;b=b.layers;Qg.call(this,a);this.f=[];this.a={};B(this,Za("layers"),this.Ok,this);b?Array.isArray(b)?b=new re(b.slice()):la(b instanceof re,43):b=new re;this.lh(b)}w(zi,Qg);k=zi.prototype;k.ae=function(){this.Bb()&&this.u()};
k.Ok=function(){this.f.forEach(Ea);this.f.length=0;var a=this.Rc();this.f.push(B(a,ue,this.Nk,this),B(a,ve,this.Pk,this));for(var b in this.a)this.a[b].forEach(Ea);Aa(this.a);var a=a.a,c,d;b=0;for(c=a.length;b<c;b++)d=a[b],this.a[x(d).toString()]=[B(d,"propertychange",this.ae,this),B(d,"change",this.ae,this)];this.u()};k.Nk=function(a){a=a.element;var b=x(a).toString();this.a[b]=[B(a,"propertychange",this.ae,this),B(a,"change",this.ae,this)];this.u()};
k.Pk=function(a){a=x(a.element).toString();this.a[a].forEach(Ea);delete this.a[a];this.u()};k.Rc=function(){return this.get("layers")};k.lh=function(a){this.set("layers",a)};
k.ff=function(a){var b=void 0!==a?a:[],c=b.length;this.Rc().forEach(function(a){a.ff(b)});a=Rg(this);var d,e;for(d=b.length;c<d;c++)e=b[c],e.opacity*=a.opacity,e.visible=e.visible&&a.visible,e.maxResolution=Math.min(e.maxResolution,a.maxResolution),e.minResolution=Math.max(e.minResolution,a.minResolution),void 0!==a.extent&&(e.extent=void 0!==e.extent?jc(e.extent,a.extent):a.extent);return b};k.hf=function(){return"ready"};function Ai(a){sc.call(this,{code:a,units:"m",extent:Bi,global:!0,worldExtent:Ci})}w(Ai,sc);Ai.prototype.getPointResolution=function(a,b){return a/na(b[1]/6378137)};var Di=6378137*Math.PI,Bi=[-Di,-Di,Di,Di],Ci=[-180,-85,180,85],Ec="EPSG:3857 EPSG:102100 EPSG:102113 EPSG:900913 urn:ogc:def:crs:EPSG:6.18:3:3857 urn:ogc:def:crs:EPSG::3857 http://www.opengis.net/gml/srs/epsg.xml#3857".split(" ").map(function(a){return new Ai(a)});
function Fc(a,b,c){var d=a.length;c=1<c?c:2;void 0===b&&(2<c?b=a.slice():b=Array(d));for(var e=0;e<d;e+=c)b[e]=6378137*Math.PI*a[e]/180,b[e+1]=6378137*Math.log(Math.tan(Math.PI*(a[e+1]+90)/360));return b}function Gc(a,b,c){var d=a.length;c=1<c?c:2;void 0===b&&(2<c?b=a.slice():b=Array(d));for(var e=0;e<d;e+=c)b[e]=180*a[e]/(6378137*Math.PI),b[e+1]=360*Math.atan(Math.exp(a[e+1]/6378137))/Math.PI-90;return b};var Ei=new pc(6378137);function Fi(a,b){sc.call(this,{code:a,units:"degrees",extent:Gi,axisOrientation:b,global:!0,metersPerUnit:Hi,worldExtent:Gi})}w(Fi,sc);Fi.prototype.getPointResolution=function(a){return a};
var Gi=[-180,-90,180,90],Hi=Math.PI*Ei.radius/180,Hc=[new Fi("CRS:84"),new Fi("EPSG:4326","neu"),new Fi("urn:ogc:def:crs:EPSG::4326","neu"),new Fi("urn:ogc:def:crs:EPSG:6.6:4326","neu"),new Fi("urn:ogc:def:crs:OGC:1.3:CRS84"),new Fi("urn:ogc:def:crs:OGC:2:84"),new Fi("http://www.opengis.net/gml/srs/epsg.xml#4326","neu"),new Fi("urn:x-ogc:def:crs:EPSG:4326","neu")];function Ii(){wc(Ec);wc(Hc);Dc()};function Ji(a){Wg.call(this,a?a:{})}w(Ji,Wg);function Ki(a){a=a?a:{};var b=za({},a);delete b.preload;delete b.useInterimTilesOnError;Wg.call(this,b);this.l(void 0!==a.preload?a.preload:0);this.A(void 0!==a.useInterimTilesOnError?a.useInterimTilesOnError:!0)}w(Ki,Wg);Ki.prototype.f=function(){return this.get("preload")};Ki.prototype.l=function(a){this.set("preload",a)};Ki.prototype.c=function(){return this.get("useInterimTilesOnError")};Ki.prototype.A=function(a){this.set("useInterimTilesOnError",a)};var Li=[0,0,0,1],Mi=[],Ni=[0,0,0,1];function Oi(a,b,c,d){0!==b&&(a.translate(c,d),a.rotate(b),a.translate(-c,-d))};function Pi(a){this.v=a.opacity;this.U=a.rotateWithView;this.o=a.rotation;this.i=a.scale;this.H=a.snapToPixel}k=Pi.prototype;k.oe=function(){return this.v};k.pe=function(){return this.U};k.qe=function(){return this.o};k.re=function(){return this.i};k.Xd=function(){return this.H};k.se=function(a){this.v=a};k.te=function(a){this.o=a};k.ue=function(a){this.i=a};function Qi(a){a=a||{};this.l=this.f=this.c=null;this.g=void 0!==a.fill?a.fill:null;this.b=void 0!==a.stroke?a.stroke:null;this.a=a.radius;this.A=[0,0];this.s=this.D=this.j=null;var b=a.atlasManager,c,d=null,e,f=0;this.b&&(e=Ce(this.b.b),f=this.b.a,void 0===f&&(f=1),d=this.b.g,Lf||(d=null));var g=2*(this.a+f)+1;e={strokeStyle:e,Ad:f,size:g,lineDash:d};if(void 0===b)b=$e(g,g),this.f=b.canvas,c=g=this.f.width,this.Dh(e,b,0,0),this.g?this.l=this.f:(b=$e(e.size,e.size),this.l=b.canvas,this.Ch(e,b,0,0));
else{g=Math.round(g);(d=!this.g)&&(c=this.Ch.bind(this,e));var f=this.b?Ri(this.b):"-",h=this.g?Si(this.g):"-";this.c&&f==this.c[1]&&h==this.c[2]&&this.a==this.c[3]||(this.c=["c"+f+h+(void 0!==this.a?this.a.toString():"-"),f,h,this.a]);b=b.add(this.c[0],g,g,this.Dh.bind(this,e),c);this.f=b.image;this.A=[b.offsetX,b.offsetY];c=b.image.width;this.l=d?b.Qg:this.f}this.j=[g/2,g/2];this.D=[g,g];this.s=[c,c];Pi.call(this,{opacity:1,rotateWithView:!1,rotation:0,scale:1,snapToPixel:void 0!==a.snapToPixel?
a.snapToPixel:!0})}w(Qi,Pi);k=Qi.prototype;k.bc=function(){return this.j};k.wn=function(){return this.g};k.ne=function(){return this.l};k.mc=function(){return this.f};k.sd=function(){return 2};k.kd=function(){return this.s};k.Ka=function(){return this.A};k.xn=function(){return this.a};k.Jb=function(){return this.D};k.yn=function(){return this.b};k.nf=ha;k.load=ha;k.Wf=ha;
k.Dh=function(a,b,c,d){b.setTransform(1,0,0,1,0,0);b.translate(c,d);b.beginPath();b.arc(a.size/2,a.size/2,this.a,0,2*Math.PI,!0);this.g&&(b.fillStyle=Ee(this.g.b),b.fill());this.b&&(b.strokeStyle=a.strokeStyle,b.lineWidth=a.Ad,a.lineDash&&b.setLineDash(a.lineDash),b.stroke());b.closePath()};
k.Ch=function(a,b,c,d){b.setTransform(1,0,0,1,0,0);b.translate(c,d);b.beginPath();b.arc(a.size/2,a.size/2,this.a,0,2*Math.PI,!0);b.fillStyle=Ce(Li);b.fill();this.b&&(b.strokeStyle=a.strokeStyle,b.lineWidth=a.Ad,a.lineDash&&b.setLineDash(a.lineDash),b.stroke());b.closePath()};function Ti(a){a=a||{};this.b=void 0!==a.color?a.color:null;this.a=void 0}Ti.prototype.g=function(){return this.b};Ti.prototype.f=function(a){this.b=a;this.a=void 0};function Si(a){void 0===a.a&&(a.a=a.b instanceof CanvasPattern||a.b instanceof CanvasGradient?x(a.b).toString():"f"+(a.b?Ce(a.b):"-"));return a.a};function Ui(){this.a=-1};function Vi(){this.a=64;this.b=Array(4);this.c=Array(this.a);this.b[0]=1732584193;this.b[1]=4023233417;this.b[2]=2562383102;this.b[3]=271733878;this.f=this.g=0}(function(){function a(){}a.prototype=Ui.prototype;Vi.a=Ui.prototype;Vi.prototype=new a;Vi.prototype.constructor=Vi;Vi.b=function(a,c,d){for(var e=Array(arguments.length-2),f=2;f<arguments.length;f++)e[f-2]=arguments[f];return Ui.prototype[c].apply(a,e)}})();
function Wi(a,b,c){c||(c=0);var d=Array(16);if("string"==typeof b)for(var e=0;16>e;++e)d[e]=b.charCodeAt(c++)|b.charCodeAt(c++)<<8|b.charCodeAt(c++)<<16|b.charCodeAt(c++)<<24;else for(e=0;16>e;++e)d[e]=b[c++]|b[c++]<<8|b[c++]<<16|b[c++]<<24;b=a.b[0];c=a.b[1];var e=a.b[2],f=a.b[3],g;g=b+(f^c&(e^f))+d[0]+3614090360&4294967295;b=c+(g<<7&4294967295|g>>>25);g=f+(e^b&(c^e))+d[1]+3905402710&4294967295;f=b+(g<<12&4294967295|g>>>20);g=e+(c^f&(b^c))+d[2]+606105819&4294967295;e=f+(g<<17&4294967295|g>>>15);g=
c+(b^e&(f^b))+d[3]+3250441966&4294967295;c=e+(g<<22&4294967295|g>>>10);g=b+(f^c&(e^f))+d[4]+4118548399&4294967295;b=c+(g<<7&4294967295|g>>>25);g=f+(e^b&(c^e))+d[5]+1200080426&4294967295;f=b+(g<<12&4294967295|g>>>20);g=e+(c^f&(b^c))+d[6]+2821735955&4294967295;e=f+(g<<17&4294967295|g>>>15);g=c+(b^e&(f^b))+d[7]+4249261313&4294967295;c=e+(g<<22&4294967295|g>>>10);g=b+(f^c&(e^f))+d[8]+1770035416&4294967295;b=c+(g<<7&4294967295|g>>>25);g=f+(e^b&(c^e))+d[9]+2336552879&4294967295;f=b+(g<<12&4294967295|g>>>
20);g=e+(c^f&(b^c))+d[10]+4294925233&4294967295;e=f+(g<<17&4294967295|g>>>15);g=c+(b^e&(f^b))+d[11]+2304563134&4294967295;c=e+(g<<22&4294967295|g>>>10);g=b+(f^c&(e^f))+d[12]+1804603682&4294967295;b=c+(g<<7&4294967295|g>>>25);g=f+(e^b&(c^e))+d[13]+4254626195&4294967295;f=b+(g<<12&4294967295|g>>>20);g=e+(c^f&(b^c))+d[14]+2792965006&4294967295;e=f+(g<<17&4294967295|g>>>15);g=c+(b^e&(f^b))+d[15]+1236535329&4294967295;c=e+(g<<22&4294967295|g>>>10);g=b+(e^f&(c^e))+d[1]+4129170786&4294967295;b=c+(g<<5&4294967295|
g>>>27);g=f+(c^e&(b^c))+d[6]+3225465664&4294967295;f=b+(g<<9&4294967295|g>>>23);g=e+(b^c&(f^b))+d[11]+643717713&4294967295;e=f+(g<<14&4294967295|g>>>18);g=c+(f^b&(e^f))+d[0]+3921069994&4294967295;c=e+(g<<20&4294967295|g>>>12);g=b+(e^f&(c^e))+d[5]+3593408605&4294967295;b=c+(g<<5&4294967295|g>>>27);g=f+(c^e&(b^c))+d[10]+38016083&4294967295;f=b+(g<<9&4294967295|g>>>23);g=e+(b^c&(f^b))+d[15]+3634488961&4294967295;e=f+(g<<14&4294967295|g>>>18);g=c+(f^b&(e^f))+d[4]+3889429448&4294967295;c=e+(g<<20&4294967295|
g>>>12);g=b+(e^f&(c^e))+d[9]+568446438&4294967295;b=c+(g<<5&4294967295|g>>>27);g=f+(c^e&(b^c))+d[14]+3275163606&4294967295;f=b+(g<<9&4294967295|g>>>23);g=e+(b^c&(f^b))+d[3]+4107603335&4294967295;e=f+(g<<14&4294967295|g>>>18);g=c+(f^b&(e^f))+d[8]+1163531501&4294967295;c=e+(g<<20&4294967295|g>>>12);g=b+(e^f&(c^e))+d[13]+2850285829&4294967295;b=c+(g<<5&4294967295|g>>>27);g=f+(c^e&(b^c))+d[2]+4243563512&4294967295;f=b+(g<<9&4294967295|g>>>23);g=e+(b^c&(f^b))+d[7]+1735328473&4294967295;e=f+(g<<14&4294967295|
g>>>18);g=c+(f^b&(e^f))+d[12]+2368359562&4294967295;c=e+(g<<20&4294967295|g>>>12);g=b+(c^e^f)+d[5]+4294588738&4294967295;b=c+(g<<4&4294967295|g>>>28);g=f+(b^c^e)+d[8]+2272392833&4294967295;f=b+(g<<11&4294967295|g>>>21);g=e+(f^b^c)+d[11]+1839030562&4294967295;e=f+(g<<16&4294967295|g>>>16);g=c+(e^f^b)+d[14]+4259657740&4294967295;c=e+(g<<23&4294967295|g>>>9);g=b+(c^e^f)+d[1]+2763975236&4294967295;b=c+(g<<4&4294967295|g>>>28);g=f+(b^c^e)+d[4]+1272893353&4294967295;f=b+(g<<11&4294967295|g>>>21);g=e+(f^
b^c)+d[7]+4139469664&4294967295;e=f+(g<<16&4294967295|g>>>16);g=c+(e^f^b)+d[10]+3200236656&4294967295;c=e+(g<<23&4294967295|g>>>9);g=b+(c^e^f)+d[13]+681279174&4294967295;b=c+(g<<4&4294967295|g>>>28);g=f+(b^c^e)+d[0]+3936430074&4294967295;f=b+(g<<11&4294967295|g>>>21);g=e+(f^b^c)+d[3]+3572445317&4294967295;e=f+(g<<16&4294967295|g>>>16);g=c+(e^f^b)+d[6]+76029189&4294967295;c=e+(g<<23&4294967295|g>>>9);g=b+(c^e^f)+d[9]+3654602809&4294967295;b=c+(g<<4&4294967295|g>>>28);g=f+(b^c^e)+d[12]+3873151461&4294967295;
f=b+(g<<11&4294967295|g>>>21);g=e+(f^b^c)+d[15]+530742520&4294967295;e=f+(g<<16&4294967295|g>>>16);g=c+(e^f^b)+d[2]+3299628645&4294967295;c=e+(g<<23&4294967295|g>>>9);g=b+(e^(c|~f))+d[0]+4096336452&4294967295;b=c+(g<<6&4294967295|g>>>26);g=f+(c^(b|~e))+d[7]+1126891415&4294967295;f=b+(g<<10&4294967295|g>>>22);g=e+(b^(f|~c))+d[14]+2878612391&4294967295;e=f+(g<<15&4294967295|g>>>17);g=c+(f^(e|~b))+d[5]+4237533241&4294967295;c=e+(g<<21&4294967295|g>>>11);g=b+(e^(c|~f))+d[12]+1700485571&4294967295;b=c+
(g<<6&4294967295|g>>>26);g=f+(c^(b|~e))+d[3]+2399980690&4294967295;f=b+(g<<10&4294967295|g>>>22);g=e+(b^(f|~c))+d[10]+4293915773&4294967295;e=f+(g<<15&4294967295|g>>>17);g=c+(f^(e|~b))+d[1]+2240044497&4294967295;c=e+(g<<21&4294967295|g>>>11);g=b+(e^(c|~f))+d[8]+1873313359&4294967295;b=c+(g<<6&4294967295|g>>>26);g=f+(c^(b|~e))+d[15]+4264355552&4294967295;f=b+(g<<10&4294967295|g>>>22);g=e+(b^(f|~c))+d[6]+2734768916&4294967295;e=f+(g<<15&4294967295|g>>>17);g=c+(f^(e|~b))+d[13]+1309151649&4294967295;
c=e+(g<<21&4294967295|g>>>11);g=b+(e^(c|~f))+d[4]+4149444226&4294967295;b=c+(g<<6&4294967295|g>>>26);g=f+(c^(b|~e))+d[11]+3174756917&4294967295;f=b+(g<<10&4294967295|g>>>22);g=e+(b^(f|~c))+d[2]+718787259&4294967295;e=f+(g<<15&4294967295|g>>>17);g=c+(f^(e|~b))+d[9]+3951481745&4294967295;a.b[0]=a.b[0]+b&4294967295;a.b[1]=a.b[1]+(e+(g<<21&4294967295|g>>>11))&4294967295;a.b[2]=a.b[2]+e&4294967295;a.b[3]=a.b[3]+f&4294967295}
function Xi(a,b){var c;void 0===c&&(c=b.length);for(var d=c-a.a,e=a.c,f=a.g,g=0;g<c;){if(0==f)for(;g<=d;)Wi(a,b,g),g+=a.a;if("string"==typeof b)for(;g<c;){if(e[f++]=b.charCodeAt(g++),f==a.a){Wi(a,e);f=0;break}}else for(;g<c;)if(e[f++]=b[g++],f==a.a){Wi(a,e);f=0;break}}a.g=f;a.f+=c};function Yi(a){a=a||{};this.b=void 0!==a.color?a.color:null;this.f=a.lineCap;this.g=void 0!==a.lineDash?a.lineDash:null;this.c=a.lineJoin;this.i=a.miterLimit;this.a=a.width;this.l=void 0}k=Yi.prototype;k.Fn=function(){return this.b};k.ak=function(){return this.f};k.Gn=function(){return this.g};k.bk=function(){return this.c};k.gk=function(){return this.i};k.Hn=function(){return this.a};k.In=function(a){this.b=a;this.l=void 0};k.bp=function(a){this.f=a;this.l=void 0};
k.Jn=function(a){this.g=a;this.l=void 0};k.cp=function(a){this.c=a;this.l=void 0};k.ep=function(a){this.i=a;this.l=void 0};k.ip=function(a){this.a=a;this.l=void 0};
function Ri(a){if(void 0===a.l){var b="s"+(a.b?Ce(a.b):"-")+","+(void 0!==a.f?a.f.toString():"-")+","+(a.g?a.g.toString():"-")+","+(void 0!==a.c?a.c:"-")+","+(void 0!==a.i?a.i.toString():"-")+","+(void 0!==a.a?a.a.toString():"-"),c=new Vi;Xi(c,b);b=Array((56>c.g?c.a:2*c.a)-c.g);b[0]=128;for(var d=1;d<b.length-8;++d)b[d]=0;for(var e=8*c.f,d=b.length-8;d<b.length;++d)b[d]=e&255,e/=256;Xi(c,b);b=Array(16);for(d=e=0;4>d;++d)for(var f=0;32>f;f+=8)b[e++]=c.b[d]>>>f&255;if(8192>=b.length)c=String.fromCharCode.apply(null,
b);else for(c="",d=0;d<b.length;d+=8192)e=He(b,d,d+8192),c+=String.fromCharCode.apply(null,e);a.l=c}return a.l};function Zi(a){a=a||{};this.i=null;this.g=$i;void 0!==a.geometry&&this.Gh(a.geometry);this.c=void 0!==a.fill?a.fill:null;this.a=void 0!==a.image?a.image:null;this.f=void 0!==a.stroke?a.stroke:null;this.l=void 0!==a.text?a.text:null;this.b=a.zIndex}k=Zi.prototype;k.Y=function(){return this.i};k.Wj=function(){return this.g};k.Kn=function(){return this.c};k.Ln=function(){return this.a};k.Mn=function(){return this.f};k.Ja=function(){return this.l};k.Nn=function(){return this.b};
k.Gh=function(a){"function"===typeof a?this.g=a:"string"===typeof a?this.g=function(b){return b.get(a)}:a?a&&(this.g=function(){return a}):this.g=$i;this.i=a};k.On=function(a){this.b=a};function aj(a){if("function"!==typeof a){var b;Array.isArray(a)?b=a:(la(a instanceof Zi,41),b=[a]);a=function(){return b}}return a}var bj=null;
function cj(){if(!bj){var a=new Ti({color:"rgba(255,255,255,0.4)"}),b=new Yi({color:"#3399CC",width:1.25});bj=[new Zi({image:new Qi({fill:a,stroke:b,radius:5}),fill:a,stroke:b})]}return bj}
function dj(){var a={},b=[255,255,255,1],c=[0,153,255,1];a.Polygon=[new Zi({fill:new Ti({color:[255,255,255,.5]})})];a.MultiPolygon=a.Polygon;a.LineString=[new Zi({stroke:new Yi({color:b,width:5})}),new Zi({stroke:new Yi({color:c,width:3})})];a.MultiLineString=a.LineString;a.Circle=a.Polygon.concat(a.LineString);a.Point=[new Zi({image:new Qi({radius:6,fill:new Ti({color:c}),stroke:new Yi({color:b,width:1.5})}),zIndex:Infinity})];a.MultiPoint=a.Point;a.GeometryCollection=a.Polygon.concat(a.LineString,
a.Point);return a}function $i(a){return a.Y()};function F(a){a=a?a:{};var b=za({},a);delete b.style;delete b.renderBuffer;delete b.updateWhileAnimating;delete b.updateWhileInteracting;Wg.call(this,b);this.a=void 0!==a.renderBuffer?a.renderBuffer:100;this.A=null;this.i=void 0;this.l(a.style);this.R=void 0!==a.updateWhileAnimating?a.updateWhileAnimating:!1;this.T=void 0!==a.updateWhileInteracting?a.updateWhileInteracting:!1}w(F,Wg);function ej(a){return a.get("renderOrder")}F.prototype.H=function(){return this.A};F.prototype.D=function(){return this.i};
F.prototype.l=function(a){this.A=void 0!==a?a:cj;this.i=null===a?void 0:aj(this.A);this.u()};function H(a){a=a?a:{};var b=za({},a);delete b.preload;delete b.useInterimTilesOnError;F.call(this,b);this.W(a.preload?a.preload:0);this.ha(a.useInterimTilesOnError?a.useInterimTilesOnError:!0);la(void 0==a.renderMode||"image"==a.renderMode||"hybrid"==a.renderMode||"vector"==a.renderMode,28);this.s=a.renderMode||"hybrid"}w(H,F);H.prototype.f=function(){return this.get("preload")};H.prototype.c=function(){return this.get("useInterimTilesOnError")};H.prototype.W=function(a){this.set("preload",a)};
H.prototype.ha=function(a){this.set("useInterimTilesOnError",a)};function fj(){};function gj(a,b,c,d,e){this.f=a;this.A=b;this.c=c;this.H=d;this.Mb=e;this.i=this.b=this.a=this.Va=this.W=this.T=null;this.cb=this.ha=this.v=this.Aa=this.O=this.D=0;this.pa=!1;this.l=this.na=0;this.Lb=!1;this.Ba=0;this.g="";this.Ha=this.za=0;this.Ia=!1;this.o=this.sb=0;this.R=this.s=this.j=null;this.U=[];this.tb=ah()}w(gj,fj);
function hj(a,b,c){if(a.i){b=Rc(b,0,c,2,a.H,a.U);c=a.f;var d=a.tb,e=c.globalAlpha;1!=a.v&&(c.globalAlpha=e*a.v);var f=a.na;a.pa&&(f+=a.Mb);var g,h;g=0;for(h=b.length;g<h;g+=2){var l=b[g]-a.D,m=b[g+1]-a.O;a.Lb&&(l=Math.round(l),m=Math.round(m));if(0!==f||1!=a.l){var n=l+a.D,p=m+a.O;jh(d,n,p,a.l,a.l,f,-n,-p);c.setTransform.apply(c,d)}c.drawImage(a.i,a.ha,a.cb,a.Ba,a.Aa,l,m,a.Ba,a.Aa)}0===f&&1==a.l||c.setTransform(1,0,0,1,0,0);1!=a.v&&(c.globalAlpha=e)}}
function ij(a,b,c,d){var e=0;if(a.R&&""!==a.g){a.j&&jj(a,a.j);a.s&&kj(a,a.s);var f=a.R,g=a.f,h=a.Va;h?(h.font!=f.font&&(h.font=g.font=f.font),h.textAlign!=f.textAlign&&(h.textAlign=g.textAlign=f.textAlign),h.textBaseline!=f.textBaseline&&(h.textBaseline=g.textBaseline=f.textBaseline)):(g.font=f.font,g.textAlign=f.textAlign,g.textBaseline=f.textBaseline,a.Va={font:f.font,textAlign:f.textAlign,textBaseline:f.textBaseline});b=Rc(b,e,c,d,a.H,a.U);f=a.f;g=a.sb;for(a.Ia&&(g+=a.Mb);e<c;e+=d){var h=b[e]+
a.za,l=b[e+1]+a.Ha;if(0!==g||1!=a.o){var m=jh(a.tb,h,l,a.o,a.o,g,-h,-l);f.setTransform.apply(f,m)}a.s&&f.strokeText(a.g,h,l);a.j&&f.fillText(a.g,h,l)}0===g&&1==a.o||f.setTransform(1,0,0,1,0,0)}}function lj(a,b,c,d,e,f){var g=a.f;a=Rc(b,c,d,e,a.H,a.U);g.moveTo(a[0],a[1]);b=a.length;f&&(b-=2);for(c=2;c<b;c+=2)g.lineTo(a[c],a[c+1]);f&&g.closePath();return d}function mj(a,b,c,d,e){var f,g;f=0;for(g=d.length;f<g;++f)c=lj(a,b,c,d[f],e,!0);return c}k=gj.prototype;
k.Qd=function(a){if(kc(this.c,a.C())){if(this.a||this.b){this.a&&jj(this,this.a);this.b&&kj(this,this.b);var b;b=this.H;var c=this.U,d=a.la();b=d?Rc(d,0,d.length,a.ua(),b,c):null;c=b[2]-b[0];d=b[3]-b[1];c=Math.sqrt(c*c+d*d);d=this.f;d.beginPath();d.arc(b[0],b[1],c,0,2*Math.PI);this.a&&d.fill();this.b&&d.stroke()}""!==this.g&&ij(this,a.qd(),2,2)}};k.rd=function(a){this.Wb(a.c,a.f);this.Yb(a.a);this.$b(a.Ja())};
k.sc=function(a){switch(a.X()){case "Point":this.uc(a);break;case "LineString":this.hd(a);break;case "Polygon":this.$e(a);break;case "MultiPoint":this.tc(a);break;case "MultiLineString":this.Ye(a);break;case "MultiPolygon":this.Ze(a);break;case "GeometryCollection":this.Xe(a);break;case "Circle":this.Qd(a)}};k.We=function(a,b){var c=(0,b.g)(a);c&&kc(this.c,c.C())&&(this.rd(b),this.sc(c))};k.Xe=function(a){a=a.f;var b,c;b=0;for(c=a.length;b<c;++b)this.sc(a[b])};
k.uc=function(a){var b=a.la();a=a.ua();this.i&&hj(this,b,b.length);""!==this.g&&ij(this,b,b.length,a)};k.tc=function(a){var b=a.la();a=a.ua();this.i&&hj(this,b,b.length);""!==this.g&&ij(this,b,b.length,a)};k.hd=function(a){if(kc(this.c,a.C())){if(this.b){kj(this,this.b);var b=this.f,c=a.la();b.beginPath();lj(this,c,0,c.length,a.ua(),!1);b.stroke()}""!==this.g&&(a=nj(a),ij(this,a,2,2))}};
k.Ye=function(a){var b=a.C();if(kc(this.c,b)){if(this.b){kj(this,this.b);var b=this.f,c=a.la(),d=0,e=a.Hb(),f=a.ua();b.beginPath();var g,h;g=0;for(h=e.length;g<h;++g)d=lj(this,c,d,e[g],f,!1);b.stroke()}""!==this.g&&(a=oj(a),ij(this,a,a.length,2))}};k.$e=function(a){if(kc(this.c,a.C())){if(this.b||this.a){this.a&&jj(this,this.a);this.b&&kj(this,this.b);var b=this.f;b.beginPath();mj(this,a.Qb(),0,a.Hb(),a.ua());this.a&&b.fill();this.b&&b.stroke()}""!==this.g&&(a=wd(a),ij(this,a,2,2))}};
k.Ze=function(a){if(kc(this.c,a.C())){if(this.b||this.a){this.a&&jj(this,this.a);this.b&&kj(this,this.b);var b=this.f,c=pj(a),d=0,e=a.c,f=a.ua(),g,h;g=0;for(h=e.length;g<h;++g){var l=e[g];b.beginPath();d=mj(this,c,d,l,f);this.a&&b.fill();this.b&&b.stroke()}}""!==this.g&&(a=qj(a),ij(this,a,a.length,2))}};function jj(a,b){var c=a.f,d=a.T;d?d.fillStyle!=b.fillStyle&&(d.fillStyle=c.fillStyle=b.fillStyle):(c.fillStyle=b.fillStyle,a.T={fillStyle:b.fillStyle})}
function kj(a,b){var c=a.f,d=a.W;d?(d.lineCap!=b.lineCap&&(d.lineCap=c.lineCap=b.lineCap),Lf&&!hb(d.lineDash,b.lineDash)&&c.setLineDash(d.lineDash=b.lineDash),d.lineJoin!=b.lineJoin&&(d.lineJoin=c.lineJoin=b.lineJoin),d.lineWidth!=b.lineWidth&&(d.lineWidth=c.lineWidth=b.lineWidth),d.miterLimit!=b.miterLimit&&(d.miterLimit=c.miterLimit=b.miterLimit),d.strokeStyle!=b.strokeStyle&&(d.strokeStyle=c.strokeStyle=b.strokeStyle)):(c.lineCap=b.lineCap,Lf&&c.setLineDash(b.lineDash),c.lineJoin=b.lineJoin,c.lineWidth=
b.lineWidth,c.miterLimit=b.miterLimit,c.strokeStyle=b.strokeStyle,a.W={lineCap:b.lineCap,lineDash:b.lineDash,lineJoin:b.lineJoin,lineWidth:b.lineWidth,miterLimit:b.miterLimit,strokeStyle:b.strokeStyle})}
k.Wb=function(a,b){if(a){var c=a.b;this.a={fillStyle:Ee(c?c:Li)}}else this.a=null;if(b){var c=b.b,d=b.f,e=b.g,f=b.c,g=b.a,h=b.i;this.b={lineCap:void 0!==d?d:"round",lineDash:e?e:Mi,lineJoin:void 0!==f?f:"round",lineWidth:this.A*(void 0!==g?g:1),miterLimit:void 0!==h?h:10,strokeStyle:Ce(c?c:Ni)}}else this.b=null};
k.Yb=function(a){if(a){var b=a.bc(),c=a.mc(1),d=a.Ka(),e=a.Jb();this.D=b[0];this.O=b[1];this.Aa=e[1];this.i=c;this.v=a.v;this.ha=d[0];this.cb=d[1];this.pa=a.U;this.na=a.o;this.l=a.i;this.Lb=a.H;this.Ba=e[0]}else this.i=null};
k.$b=function(a){if(a){var b=a.b;b?(b=b.b,this.j={fillStyle:Ee(b?b:Li)}):this.j=null;var c=a.l;if(c){var b=c.b,d=c.f,e=c.g,f=c.c,g=c.a,c=c.i;this.s={lineCap:void 0!==d?d:"round",lineDash:e?e:Mi,lineJoin:void 0!==f?f:"round",lineWidth:void 0!==g?g:1,miterLimit:void 0!==c?c:10,strokeStyle:Ce(b?b:Ni)}}else this.s=null;var b=a.g,d=a.f,e=a.c,f=a.s,g=a.i,c=a.a,h=a.Ja(),l=a.j;a=a.o;this.R={font:void 0!==b?b:"10px sans-serif",textAlign:void 0!==l?l:"center",textBaseline:void 0!==a?a:"middle"};this.g=void 0!==
h?h:"";this.za=void 0!==d?this.A*d:0;this.Ha=void 0!==e?this.A*e:0;this.Ia=void 0!==f?f:!1;this.sb=void 0!==g?g:0;this.o=this.A*(void 0!==c?c:1)}else this.g=""};function rj(a,b,c,d,e){Sa.call(this);this.l=e;this.extent=a;this.f=c;this.resolution=b;this.state=d}w(rj,Sa);function sj(a){a.b("change")}rj.prototype.C=function(){return this.extent};rj.prototype.$=function(){return this.resolution};rj.prototype.V=function(){return this.state};function tj(a){Ua.call(this);this.a=a}w(tj,Ua);k=tj.prototype;k.ta=ha;k.Ac=function(a,b,c,d){a=fh(b.pixelToCoordinateTransform,a.slice());if(this.ta(a,b,nc,this))return c.call(d,this.a,null)};k.je=oc;k.Pd=function(a,b,c){return function(d,e){return uj(a,b,d,e,function(a){c[d]||(c[d]={});c[d][a.oa.toString()]=a})}};k.Jm=function(a){2===a.target.V()&&vj(this)};function wj(a,b){var c=b.V();2!=c&&3!=c&&B(b,"change",a.Jm,a);0==c&&(b.load(),c=b.V());return 2==c}
function vj(a){var b=a.a;b.Bb()&&"ready"==b.hf()&&a.u()}function xj(a,b){b.xh()&&a.postRenderFunctions.push(function(a,b,e){b=x(a).toString();a.Jc(e.viewState.projection,e.usedTiles[b])}.bind(null,b))}function yj(a,b){if(b){var c,d,e;d=0;for(e=b.length;d<e;++d)c=b[d],a[x(c).toString()]=c}}function zj(a,b){var c=b.O;void 0!==c&&("string"===typeof c?a.logos[c]="":c&&(la("string"==typeof c.href,44),la("string"==typeof c.src,45),a.logos[c.src]=c.href))}
function Aj(a,b,c,d){b=x(b).toString();c=c.toString();b in a?c in a[b]?(a=a[b][c],d.ca<a.ca&&(a.ca=d.ca),d.ea>a.ea&&(a.ea=d.ea),d.fa<a.fa&&(a.fa=d.fa),d.ia>a.ia&&(a.ia=d.ia)):a[b][c]=d:(a[b]={},a[b][c]=d)}function Bj(a,b,c){return[b*(Math.round(a[0]/b)+c[0]%2/2),b*(Math.round(a[1]/b)+c[1]%2/2)]}
function Cj(a,b,c,d,e,f,g,h,l,m){var n=x(b).toString();n in a.wantedTiles||(a.wantedTiles[n]={});var p=a.wantedTiles[n];a=a.tileQueue;var q=c.minZoom,t,v,u,y,E,z;for(z=g;z>=q;--z)for(v=ge(c,f,z,v),u=c.$(z),y=v.ca;y<=v.ea;++y)for(E=v.fa;E<=v.ia;++E)g-z<=h?(t=b.dc(z,y,E,d,e),0==t.V()&&(p[t.Ya()]=!0,t.Ya()in a.a||a.c([t,n,le(c,t.oa),u])),void 0!==l&&l.call(m,t)):b.Xf(z,y,E,e)};function Dj(a){tj.call(this,a);this.O=ah()}w(Dj,tj);function Ej(a,b,c){var d=b.pixelRatio,e=b.size[0]*d,f=b.size[1]*d,g=b.viewState.rotation,h=bc(c),l=ac(c),m=$b(c);c=Zb(c);fh(b.coordinateToPixelTransform,h);fh(b.coordinateToPixelTransform,l);fh(b.coordinateToPixelTransform,m);fh(b.coordinateToPixelTransform,c);a.save();Oi(a,-g,e/2,f/2);a.beginPath();a.moveTo(h[0]*d,h[1]*d);a.lineTo(l[0]*d,l[1]*d);a.lineTo(m[0]*d,m[1]*d);a.lineTo(c[0]*d,c[1]*d);a.clip();Oi(a,g,e/2,f/2)}
Dj.prototype.i=function(a,b,c){Fj(this,"precompose",c,a,void 0);var d=this.f?this.f.a():null;if(d){var e=b.extent,f=void 0!==e;f&&Ej(c,a,e);var e=this.s,g=c.globalAlpha;c.globalAlpha=b.opacity;c.drawImage(d,0,0,+d.width,+d.height,Math.round(e[4]),Math.round(e[5]),Math.round(d.width*e[0]),Math.round(d.height*e[3]));c.globalAlpha=g;f&&c.restore()}Gj(this,c,a)};
function Fj(a,b,c,d,e){var f=a.a;if(Ta(f,b)){var g=d.size[0]*d.pixelRatio,h=d.size[1]*d.pixelRatio,l=d.viewState.rotation;Oi(c,-l,g/2,h/2);a=void 0!==e?e:Hj(a,d,0);f.b(new Sg(b,new gj(c,d.pixelRatio,d.extent,a,d.viewState.rotation),d,c,null));Oi(c,l,g/2,h/2)}}function Gj(a,b,c,d){Fj(a,"postcompose",b,c,d)}function Hj(a,b,c){var d=b.viewState,e=b.pixelRatio,f=e/d.resolution;return jh(a.O,e*b.size[0]/2,e*b.size[1]/2,f,-f,-d.rotation,-d.center[0]+c,-d.center[1])};function Ij(){};var Jj=["Polygon","LineString","Image","Text"];function Kj(a,b,c,d){this.pa=a;this.T=b;this.overlaps=d;this.f=null;this.c=0;this.resolution=c;this.Ba=this.Aa=null;this.a=[];this.coordinates=[];this.Va=ah();this.b=[];this.W=[];this.ha=ah();this.cb=ah()}w(Kj,fj);
function Lj(a,b,c,d,e,f){var g=a.coordinates.length,h=a.bf(),l=[b[c],b[c+1]],m=[NaN,NaN],n=!0,p,q,t;for(p=c+e;p<d;p+=e)m[0]=b[p],m[1]=b[p+1],t=Ob(h,m),t!==q?(n&&(a.coordinates[g++]=l[0],a.coordinates[g++]=l[1]),a.coordinates[g++]=m[0],a.coordinates[g++]=m[1],n=!1):1===t?(a.coordinates[g++]=m[0],a.coordinates[g++]=m[1],n=!1):n=!0,l[0]=m[0],l[1]=m[1],q=t;p===c+e&&(a.coordinates[g++]=l[0],a.coordinates[g++]=l[1]);f&&(a.coordinates[g++]=b[c],a.coordinates[g++]=b[c+1]);return g}
function Mj(a,b){a.Aa=[0,b,0];a.a.push(a.Aa);a.Ba=[0,b,0];a.b.push(a.Ba)}
function Nj(a,b,c,d,e,f,g,h,l){var m;hb(d,a.Va)?m=a.W:(m=Rc(a.coordinates,0,a.coordinates.length,2,d,a.W),eh(a.Va,d));d=!Ca(f);var n=0,p=g.length,q,t,v=a.ha,u=a.cb,y,E,z,G,J=0,X=0;for(a=a.a!=g||a.overlaps?0:200;n<p;){var A=g[n],Ma,ua,M,oa;switch(A[0]){case 0:q=A[1];d&&f[x(q).toString()]||!q.Y()?n=A[2]:void 0===l||kc(l,q.Y().C())?++n:n=A[2]+1;break;case 1:J>a&&(b.fill(),J=0);X>a&&(b.stroke(),X=0);J||X||b.beginPath();++n;break;case 2:q=A[1];t=m[q];A=m[q+1];z=m[q+2]-t;q=m[q+3]-A;q=Math.sqrt(z*z+q*q);
b.moveTo(t+q,A);b.arc(t,A,q,0,2*Math.PI,!0);++n;break;case 3:b.closePath();++n;break;case 4:q=A[1];t=A[2];Ma=A[3];ua=A[4]*c;M=A[5]*c;var Wb=A[6],Ib=A[7],Jb=A[8],Yb=A[9];oa=A[10];z=A[11];G=A[12];var hc=A[13],Pb=A[14];for(oa&&(z+=e);q<t;q+=2){A=m[q]-ua;oa=m[q+1]-M;hc&&(A=Math.round(A),oa=Math.round(oa));if(1!=G||0!==z){var Od=A+ua,tf=oa+M;jh(v,Od,tf,G,G,z,-Od,-tf);b.transform.apply(b,v)}Od=b.globalAlpha;1!=Ib&&(b.globalAlpha=Od*Ib);var tf=Pb+Jb>Ma.width?Ma.width-Jb:Pb,Pd=Wb+Yb>Ma.height?Ma.height-Yb:
Wb;b.drawImage(Ma,Jb,Yb,tf,Pd,A,oa,tf*c,Pd*c);1!=Ib&&(b.globalAlpha=Od);if(1!=G||0!==z)kh(eh(u,v)),b.transform.apply(b,u)}++n;break;case 5:q=A[1];t=A[2];M=A[3];Wb=A[4]*c;Ib=A[5]*c;z=A[6];G=A[7]*c;Ma=A[8];ua=A[9];for((oa=A[10])&&(z+=e);q<t;q+=2){A=m[q]+Wb;oa=m[q+1]+Ib;if(1!=G||0!==z)jh(v,A,oa,G,G,z,-A,-oa),b.transform.apply(b,v);Jb=M.split("\n");Yb=Jb.length;1<Yb?(hc=Math.round(1.5*b.measureText("M").width),oa-=(Yb-1)/2*hc):hc=0;for(Pb=0;Pb<Yb;Pb++)Od=Jb[Pb],ua&&b.strokeText(Od,A,oa),Ma&&b.fillText(Od,
A,oa),oa+=hc;if(1!=G||0!==z)kh(eh(u,v)),b.transform.apply(b,u)}++n;break;case 6:if(void 0!==h&&(q=A[1],q=h(q)))return q;++n;break;case 7:a?J++:b.fill();++n;break;case 8:q=A[1];t=A[2];A=m[q];oa=m[q+1];z=A+.5|0;G=oa+.5|0;if(z!==y||G!==E)b.moveTo(A,oa),y=z,E=G;for(q+=2;q<t;q+=2)if(A=m[q],oa=m[q+1],z=A+.5|0,G=oa+.5|0,q==t-2||z!==y||G!==E)b.lineTo(A,oa),y=z,E=G;++n;break;case 9:J&&(b.fill(),J=0);b.fillStyle=A[1];++n;break;case 10:y=void 0!==A[7]?A[7]:!0;E=A[2];X&&(b.stroke(),X=0);b.strokeStyle=A[1];b.lineWidth=
y?E*c:E;b.lineCap=A[3];b.lineJoin=A[4];b.miterLimit=A[5];Lf&&b.setLineDash(A[6]);E=y=NaN;++n;break;case 11:b.font=A[1];b.textAlign=A[2];b.textBaseline=A[3];++n;break;case 12:a?X++:b.stroke();++n;break;default:++n}}J&&b.fill();X&&b.stroke()}Kj.prototype.Sa=function(a,b,c,d,e){Nj(this,a,b,c,d,e,this.a,void 0,void 0)};
function Oj(a){var b=a.b;b.reverse();var c,d=b.length,e,f,g=-1;for(c=0;c<d;++c)if(e=b[c],f=e[0],6==f)g=c;else if(0==f){e[2]=c;e=a.b;for(f=c;g<f;){var h=e[g];e[g]=e[f];e[f]=h;++g;--f}g=-1}}function Pj(a,b){a.Aa[2]=a.a.length;a.Aa=null;a.Ba[2]=a.b.length;a.Ba=null;var c=[6,b];a.a.push(c);a.b.push(c)}Kj.prototype.ie=ha;Kj.prototype.bf=function(){return this.T};
function Qj(a,b,c,d){Kj.call(this,a,b,c,d);this.j=this.R=null;this.O=this.D=this.H=this.A=this.U=this.v=this.s=this.o=this.l=this.i=this.g=void 0}w(Qj,Kj);Qj.prototype.uc=function(a,b){if(this.j){Mj(this,b);var c=a.la(),d=this.coordinates.length,c=Lj(this,c,0,c.length,a.ua(),!1);this.a.push([4,d,c,this.j,this.g,this.i,this.l,this.o,this.s,this.v,this.U,this.A,this.H,this.D,this.O]);this.b.push([4,d,c,this.R,this.g,this.i,this.l,this.o,this.s,this.v,this.U,this.A,this.H,this.D,this.O]);Pj(this,b)}};
Qj.prototype.tc=function(a,b){if(this.j){Mj(this,b);var c=a.la(),d=this.coordinates.length,c=Lj(this,c,0,c.length,a.ua(),!1);this.a.push([4,d,c,this.j,this.g,this.i,this.l,this.o,this.s,this.v,this.U,this.A,this.H,this.D,this.O]);this.b.push([4,d,c,this.R,this.g,this.i,this.l,this.o,this.s,this.v,this.U,this.A,this.H,this.D,this.O]);Pj(this,b)}};Qj.prototype.ie=function(){Oj(this);this.i=this.g=void 0;this.j=this.R=null;this.O=this.D=this.A=this.U=this.v=this.s=this.o=this.H=this.l=void 0};
Qj.prototype.Yb=function(a){var b=a.bc(),c=a.Jb(),d=a.ne(1),e=a.mc(1),f=a.Ka();this.g=b[0];this.i=b[1];this.R=d;this.j=e;this.l=c[1];this.o=a.v;this.s=f[0];this.v=f[1];this.U=a.U;this.A=a.o;this.H=a.i;this.D=a.H;this.O=c[0]};function Rj(a,b,c,d){Kj.call(this,a,b,c,d);this.g={fd:void 0,ad:void 0,bd:null,cd:void 0,dd:void 0,ed:void 0,mf:0,strokeStyle:void 0,lineCap:void 0,lineDash:null,lineJoin:void 0,lineWidth:void 0,miterLimit:void 0}}w(Rj,Kj);
function Sj(a,b,c,d,e){var f=a.coordinates.length;b=Lj(a,b,c,d,e,!1);f=[8,f,b];a.a.push(f);a.b.push(f);return d}k=Rj.prototype;k.bf=function(){this.f||(this.f=Hb(this.T),0<this.c&&Gb(this.f,this.resolution*(this.c+1)/2,this.f));return this.f};
function Tj(a){var b=a.g,c=b.strokeStyle,d=b.lineCap,e=b.lineDash,f=b.lineJoin,g=b.lineWidth,h=b.miterLimit;b.fd==c&&b.ad==d&&hb(b.bd,e)&&b.cd==f&&b.dd==g&&b.ed==h||(b.mf!=a.coordinates.length&&(a.a.push([12]),b.mf=a.coordinates.length),a.a.push([10,c,g,d,f,h,e],[1]),b.fd=c,b.ad=d,b.bd=e,b.cd=f,b.dd=g,b.ed=h)}
k.hd=function(a,b){var c=this.g,d=c.lineWidth;void 0!==c.strokeStyle&&void 0!==d&&(Tj(this),Mj(this,b),this.b.push([10,c.strokeStyle,c.lineWidth,c.lineCap,c.lineJoin,c.miterLimit,c.lineDash],[1]),c=a.la(),Sj(this,c,0,c.length,a.ua()),this.b.push([12]),Pj(this,b))};
k.Ye=function(a,b){var c=this.g,d=c.lineWidth;if(void 0!==c.strokeStyle&&void 0!==d){Tj(this);Mj(this,b);this.b.push([10,c.strokeStyle,c.lineWidth,c.lineCap,c.lineJoin,c.miterLimit,c.lineDash],[1]);var c=a.Hb(),d=a.la(),e=a.ua(),f=0,g,h;g=0;for(h=c.length;g<h;++g)f=Sj(this,d,f,c[g],e);this.b.push([12]);Pj(this,b)}};k.ie=function(){this.g.mf!=this.coordinates.length&&this.a.push([12]);Oj(this);this.g=null};
k.Wb=function(a,b){var c=b.b;this.g.strokeStyle=Ce(c?c:Ni);c=b.f;this.g.lineCap=void 0!==c?c:"round";c=b.g;this.g.lineDash=c?c:Mi;c=b.c;this.g.lineJoin=void 0!==c?c:"round";c=b.a;this.g.lineWidth=void 0!==c?c:1;c=b.i;this.g.miterLimit=void 0!==c?c:10;this.g.lineWidth>this.c&&(this.c=this.g.lineWidth,this.f=null)};
function Uj(a,b,c,d){Kj.call(this,a,b,c,d);this.g={sg:void 0,fd:void 0,ad:void 0,bd:null,cd:void 0,dd:void 0,ed:void 0,fillStyle:void 0,strokeStyle:void 0,lineCap:void 0,lineDash:null,lineJoin:void 0,lineWidth:void 0,miterLimit:void 0}}w(Uj,Kj);
function Vj(a,b,c,d,e){var f=a.g,g=void 0!==f.fillStyle,f=void 0!=f.strokeStyle,h=d.length;if(!g&&!f)return d[h-1];var l=[1];a.a.push(l);a.b.push(l);for(l=0;l<h;++l){var m=d[l],n=a.coordinates.length;c=Lj(a,b,c,m,e,!0);c=[8,n,c];a.a.push(c);a.b.push(c);f&&(c=[3],a.a.push(c),a.b.push(c));c=m}b=[7];a.b.push(b);g&&a.a.push(b);f&&(g=[12],a.a.push(g),a.b.push(g));return c}k=Uj.prototype;
k.Qd=function(a,b){var c=this.g,d=c.strokeStyle;if(void 0!==c.fillStyle||void 0!==d){Wj(this);Mj(this,b);this.b.push([9,Ce(Li)]);void 0!==c.strokeStyle&&this.b.push([10,c.strokeStyle,c.lineWidth,c.lineCap,c.lineJoin,c.miterLimit,c.lineDash]);var e=a.la(),d=this.coordinates.length;Lj(this,e,0,e.length,a.ua(),!1);e=[1];d=[2,d];this.a.push(e,d);this.b.push(e,d);d=[7];this.b.push(d);void 0!==c.fillStyle&&this.a.push(d);void 0!==c.strokeStyle&&(c=[12],this.a.push(c),this.b.push(c));Pj(this,b)}};
k.$e=function(a,b){var c=this.g,d=c.strokeStyle;if(void 0!==c.fillStyle||void 0!==d)Wj(this),Mj(this,b),this.b.push([9,Ce(Li)]),void 0!==c.strokeStyle&&this.b.push([10,c.strokeStyle,c.lineWidth,c.lineCap,c.lineJoin,c.miterLimit,c.lineDash]),c=a.Hb(),d=a.Qb(),Vj(this,d,0,c,a.ua()),Pj(this,b)};
k.Ze=function(a,b){var c=this.g,d=c.strokeStyle;if(void 0!==c.fillStyle||void 0!==d){Wj(this);Mj(this,b);this.b.push([9,Ce(Li)]);void 0!==c.strokeStyle&&this.b.push([10,c.strokeStyle,c.lineWidth,c.lineCap,c.lineJoin,c.miterLimit,c.lineDash]);var c=a.c,d=pj(a),e=a.ua(),f=0,g,h;g=0;for(h=c.length;g<h;++g)f=Vj(this,d,f,c[g],e);Pj(this,b)}};k.ie=function(){Oj(this);this.g=null;var a=this.pa;if(0!==a){var b=this.coordinates,c,d;c=0;for(d=b.length;c<d;++c)b[c]=a*Math.round(b[c]/a)}};
k.bf=function(){this.f||(this.f=Hb(this.T),0<this.c&&Gb(this.f,this.resolution*(this.c+1)/2,this.f));return this.f};
k.Wb=function(a,b){var c=this.g;if(a){var d=a.b;c.fillStyle=Ee(d?d:Li)}else c.fillStyle=void 0;b?(d=b.b,c.strokeStyle=Ce(d?d:Ni),d=b.f,c.lineCap=void 0!==d?d:"round",d=b.g,c.lineDash=d?d.slice():Mi,d=b.c,c.lineJoin=void 0!==d?d:"round",d=b.a,c.lineWidth=void 0!==d?d:1,d=b.i,c.miterLimit=void 0!==d?d:10,c.lineWidth>this.c&&(this.c=c.lineWidth,this.f=null)):(c.strokeStyle=void 0,c.lineCap=void 0,c.lineDash=null,c.lineJoin=void 0,c.lineWidth=void 0,c.miterLimit=void 0)};
function Wj(a){var b=a.g,c=b.fillStyle,d=b.strokeStyle,e=b.lineCap,f=b.lineDash,g=b.lineJoin,h=b.lineWidth,l=b.miterLimit;void 0!==c&&b.sg!=c&&(a.a.push([9,c]),b.sg=b.fillStyle);void 0===d||b.fd==d&&b.ad==e&&b.bd==f&&b.cd==g&&b.dd==h&&b.ed==l||(a.a.push([10,d,h,e,g,l,f]),b.fd=d,b.ad=e,b.bd=f,b.cd=g,b.dd=h,b.ed=l)}function Xj(a,b,c,d){Kj.call(this,a,b,c,d);this.O=this.D=this.H=null;this.j="";this.s=this.o=0;this.v=void 0;this.A=this.U=0;this.l=this.i=this.g=null}w(Xj,Kj);
function Yj(a,b,c,d,e){if(""!==a.j&&a.l&&(a.g||a.i)){if(a.g){var f=a.g,g=a.H;if(!g||g.fillStyle!=f.fillStyle){var h=[9,f.fillStyle];a.a.push(h);a.b.push(h);g?g.fillStyle=f.fillStyle:a.H={fillStyle:f.fillStyle}}}a.i&&(f=a.i,g=a.D,g&&g.lineCap==f.lineCap&&g.lineDash==f.lineDash&&g.lineJoin==f.lineJoin&&g.lineWidth==f.lineWidth&&g.miterLimit==f.miterLimit&&g.strokeStyle==f.strokeStyle||(h=[10,f.strokeStyle,f.lineWidth,f.lineCap,f.lineJoin,f.miterLimit,f.lineDash,!1],a.a.push(h),a.b.push(h),g?(g.lineCap=
f.lineCap,g.lineDash=f.lineDash,g.lineJoin=f.lineJoin,g.lineWidth=f.lineWidth,g.miterLimit=f.miterLimit,g.strokeStyle=f.strokeStyle):a.D={lineCap:f.lineCap,lineDash:f.lineDash,lineJoin:f.lineJoin,lineWidth:f.lineWidth,miterLimit:f.miterLimit,strokeStyle:f.strokeStyle}));f=a.l;g=a.O;g&&g.font==f.font&&g.textAlign==f.textAlign&&g.textBaseline==f.textBaseline||(h=[11,f.font,f.textAlign,f.textBaseline],a.a.push(h),a.b.push(h),g?(g.font=f.font,g.textAlign=f.textAlign,g.textBaseline=f.textBaseline):a.O=
{font:f.font,textAlign:f.textAlign,textBaseline:f.textBaseline});Mj(a,e);f=a.coordinates.length;b=Lj(a,b,0,c,d,!1);b=[5,f,b,a.j,a.o,a.s,a.U,a.A,!!a.g,!!a.i,a.v];a.a.push(b);a.b.push(b);Pj(a,e)}}
Xj.prototype.$b=function(a){if(a){var b=a.b;b?(b=b.b,b=Ee(b?b:Li),this.g?this.g.fillStyle=b:this.g={fillStyle:b}):this.g=null;var c=a.l;if(c){var b=c.b,d=c.f,e=c.g,f=c.c,g=c.a,c=c.i,d=void 0!==d?d:"round",e=e?e.slice():Mi,f=void 0!==f?f:"round",g=void 0!==g?g:1,c=void 0!==c?c:10,b=Ce(b?b:Ni);if(this.i){var h=this.i;h.lineCap=d;h.lineDash=e;h.lineJoin=f;h.lineWidth=g;h.miterLimit=c;h.strokeStyle=b}else this.i={lineCap:d,lineDash:e,lineJoin:f,lineWidth:g,miterLimit:c,strokeStyle:b}}else this.i=null;
var l=a.g,b=a.f,d=a.c,e=a.s,g=a.i,c=a.a,f=a.Ja(),h=a.j,m=a.o;a=void 0!==l?l:"10px sans-serif";h=void 0!==h?h:"center";m=void 0!==m?m:"middle";this.l?(l=this.l,l.font=a,l.textAlign=h,l.textBaseline=m):this.l={font:a,textAlign:h,textBaseline:m};this.j=void 0!==f?f:"";this.o=void 0!==b?b:0;this.s=void 0!==d?d:0;this.v=void 0!==e?e:!1;this.U=void 0!==g?g:0;this.A=void 0!==c?c:1}else this.j=""};function Zj(a,b,c,d,e){this.s=a;this.f=b;this.j=d;this.o=c;this.c=e;this.a={};this.i=$e(1,1);this.l=ah()}
w(Zj,Ij);function ak(a){for(var b in a.a){var c=a.a[b],d;for(d in c)c[d].ie()}}Zj.prototype.ta=function(a,b,c,d,e){var f=jh(this.l,.5,.5,1/b,-1/b,-c,-a[0],-a[1]),g=this.i;g.clearRect(0,0,1,1);var h;void 0!==this.c&&(h=Eb(),Fb(h,a),Gb(h,b*this.c,h));return bk(this,g,f,c,d,function(a){if(0<g.getImageData(0,0,1,1).data[3]){if(a=e(a))return a;g.clearRect(0,0,1,1)}},h)};
Zj.prototype.b=function(a,b){var c=void 0!==a?a.toString():"0",d=this.a[c];void 0===d&&(d={},this.a[c]=d);c=d[b];void 0===c&&(c=new ck[b](this.s,this.f,this.o,this.j),d[b]=c);return c};Zj.prototype.g=function(){return Ca(this.a)};
Zj.prototype.Sa=function(a,b,c,d,e,f){var g=Object.keys(this.a).map(Number);g.sort(ab);var h=this.f,l=h[0],m=h[1],n=h[2],h=h[3],l=[l,m,l,h,n,h,n,m];Rc(l,0,8,2,c,l);a.save();a.beginPath();a.moveTo(l[0],l[1]);a.lineTo(l[2],l[3]);a.lineTo(l[4],l[5]);a.lineTo(l[6],l[7]);a.clip();f=f?f:Jj;for(var p,q,l=0,m=g.length;l<m;++l)for(p=this.a[g[l].toString()],n=0,h=f.length;n<h;++n)q=p[f[n]],void 0!==q&&q.Sa(a,b,c,d,e);a.restore()};
function bk(a,b,c,d,e,f,g){var h=Object.keys(a.a).map(Number);h.sort(function(a,b){return b-a});var l,m,n,p,q;l=0;for(m=h.length;l<m;++l)for(p=a.a[h[l].toString()],n=Jj.length-1;0<=n;--n)if(q=p[Jj[n]],void 0!==q&&(q=Nj(q,b,1,c,d,e,q.b,f,g)))return q}var ck={Image:Qj,LineString:Rj,Polygon:Uj,Text:Xj};function dk(a,b){return x(a)-x(b)}function ek(a,b){var c=.5*a/b;return c*c}function fk(a,b,c,d,e,f){var g=!1,h,l;if(h=c.a)l=h.sd(),2==l||3==l?h.Wf(e,f):(0==l&&h.load(),h.nf(e,f),g=!0);if(e=(0,c.g)(b))d=e.md(d),(0,gk[d.X()])(a,d,c,b);return g}
var gk={Point:function(a,b,c,d){var e=c.a;if(e){if(2!=e.sd())return;var f=a.b(c.b,"Image");f.Yb(e);f.uc(b,d)}if(e=c.Ja())a=a.b(c.b,"Text"),a.$b(e),Yj(a,b.la(),2,2,d)},LineString:function(a,b,c,d){var e=c.f;if(e){var f=a.b(c.b,"LineString");f.Wb(null,e);f.hd(b,d)}if(e=c.Ja())a=a.b(c.b,"Text"),a.$b(e),Yj(a,nj(b),2,2,d)},Polygon:function(a,b,c,d){var e=c.c,f=c.f;if(e||f){var g=a.b(c.b,"Polygon");g.Wb(e,f);g.$e(b,d)}if(e=c.Ja())a=a.b(c.b,"Text"),a.$b(e),Yj(a,wd(b),2,2,d)},MultiPoint:function(a,b,c,d){var e=
c.a;if(e){if(2!=e.sd())return;var f=a.b(c.b,"Image");f.Yb(e);f.tc(b,d)}if(e=c.Ja())a=a.b(c.b,"Text"),a.$b(e),c=b.la(),Yj(a,c,c.length,b.ua(),d)},MultiLineString:function(a,b,c,d){var e=c.f;if(e){var f=a.b(c.b,"LineString");f.Wb(null,e);f.Ye(b,d)}if(e=c.Ja())a=a.b(c.b,"Text"),a.$b(e),b=oj(b),Yj(a,b,b.length,2,d)},MultiPolygon:function(a,b,c,d){var e=c.c,f=c.f;if(f||e){var g=a.b(c.b,"Polygon");g.Wb(e,f);g.Ze(b,d)}if(e=c.Ja())a=a.b(c.b,"Text"),a.$b(e),b=qj(b),Yj(a,b,b.length,2,d)},GeometryCollection:function(a,
b,c,d){b=b.f;var e,f;e=0;for(f=b.length;e<f;++e)(0,gk[b[e].X()])(a,b[e],c,d)},Circle:function(a,b,c,d){var e=c.c,f=c.f;if(e||f){var g=a.b(c.b,"Polygon");g.Wb(e,f);g.Qd(b,d)}if(e=c.Ja())a=a.b(c.b,"Text"),a.$b(e),Yj(a,b.qd(),2,2,d)}};function hk(a,b,c,d,e,f){this.c=void 0!==f?f:null;rj.call(this,a,b,c,void 0!==f?0:2,d);this.g=e}w(hk,rj);hk.prototype.i=function(a){this.state=a?3:2;sj(this)};hk.prototype.load=function(){0==this.state&&(this.state=1,sj(this),this.c(this.i.bind(this)))};hk.prototype.a=function(){return this.g};var ik,jk=ja,kk=-1<jk.navigator.userAgent.indexOf("OPR"),lk=-1<jk.navigator.userAgent.indexOf("Edge");ik=!(!jk.navigator.userAgent.match("CriOS")&&null!==jk.chrome&&void 0!==jk.chrome&&"Google Inc."===jk.navigator.vendor&&0==kk&&0==lk);function mk(a,b,c,d){var e=Oc(c,b,a);c=b.getPointResolution(d,c);b=b.cc();void 0!==b&&(c*=b);b=a.cc();void 0!==b&&(c/=b);a=a.getPointResolution(c,e)/c;isFinite(a)&&0<a&&(c/=a);return c}
function nk(a,b,c,d){a=c-a;b=d-b;var e=Math.sqrt(a*a+b*b);return[Math.round(c+a/e),Math.round(d+b/e)]}
function ok(a,b,c,d,e,f,g,h,l,m,n){var p=$e(Math.round(c*a),Math.round(c*b));if(0===l.length)return p.canvas;p.scale(c,c);var q=Eb();l.forEach(function(a){Vb(q,a.extent)});var t=$e(Math.round(c*ec(q)/d),Math.round(c*fc(q)/d)),v=c/d;l.forEach(function(a){t.drawImage(a.image,m,m,a.image.width-2*m,a.image.height-2*m,(a.extent[0]-q[0])*v,-(a.extent[3]-q[3])*v,ec(a.extent)*v,fc(a.extent)*v)});var u=bc(g);h.f.forEach(function(a){var b=a.source,e=a.target,g=b[1][0],h=b[1][1],l=b[2][0],m=b[2][1];a=(e[0][0]-
u[0])/f;var n=-(e[0][1]-u[1])/f,v=(e[1][0]-u[0])/f,M=-(e[1][1]-u[1])/f,oa=(e[2][0]-u[0])/f,Wb=-(e[2][1]-u[1])/f,e=b[0][0],b=b[0][1],g=g-e,h=h-b,l=l-e,m=m-b;a:{g=[[g,h,0,0,v-a],[l,m,0,0,oa-a],[0,0,g,h,M-n],[0,0,l,m,Wb-n]];h=g.length;for(l=0;l<h;l++){for(var m=l,Ib=Math.abs(g[l][l]),Jb=l+1;Jb<h;Jb++){var Yb=Math.abs(g[Jb][l]);Yb>Ib&&(Ib=Yb,m=Jb)}if(0===Ib){g=null;break a}Ib=g[m];g[m]=g[l];g[l]=Ib;for(m=l+1;m<h;m++)for(Ib=-g[m][l]/g[l][l],Jb=l;Jb<h+1;Jb++)g[m][Jb]=l==Jb?0:g[m][Jb]+Ib*g[l][Jb]}l=Array(h);
for(m=h-1;0<=m;m--)for(l[m]=g[m][h]/g[m][m],Ib=m-1;0<=Ib;Ib--)g[Ib][h]-=g[Ib][m]*l[m];g=l}g&&(p.save(),p.beginPath(),ik?(l=(a+v+oa)/3,m=(n+M+Wb)/3,h=nk(l,m,a,n),v=nk(l,m,v,M),oa=nk(l,m,oa,Wb),p.moveTo(v[0],v[1]),p.lineTo(h[0],h[1]),p.lineTo(oa[0],oa[1])):(p.moveTo(v,M),p.lineTo(a,n),p.lineTo(oa,Wb)),p.clip(),p.transform(g[0],g[2],g[1],g[3],a,n),p.translate(q[0]-e,q[3]-b),p.scale(d/c,-d/c),p.drawImage(t.canvas,0,0),p.restore())});n&&(p.save(),p.strokeStyle="black",p.lineWidth=1,h.f.forEach(function(a){var b=
a.target;a=(b[0][0]-u[0])/f;var c=-(b[0][1]-u[1])/f,d=(b[1][0]-u[0])/f,e=-(b[1][1]-u[1])/f,g=(b[2][0]-u[0])/f,b=-(b[2][1]-u[1])/f;p.beginPath();p.moveTo(d,e);p.lineTo(a,c);p.lineTo(g,b);p.closePath();p.stroke()}),p.restore());return p.canvas};function pk(a,b,c,d,e){this.g=a;this.c=b;var f={},g=Mc(this.c,this.g);this.a=function(a){var b=a[0]+"/"+a[1];f[b]||(f[b]=g(a));return f[b]};this.i=d;this.s=e*e;this.f=[];this.j=!1;this.o=this.g.a&&!!d&&!!this.g.C()&&ec(d)==ec(this.g.C());this.b=this.g.C()?ec(this.g.C()):null;this.l=this.c.C()?ec(this.c.C()):null;a=bc(c);b=ac(c);d=$b(c);c=Zb(c);e=this.a(a);var h=this.a(b),l=this.a(d),m=this.a(c);qk(this,a,b,d,c,e,h,l,m,10);if(this.j){var n=Infinity;this.f.forEach(function(a){n=Math.min(n,a.source[0][0],
a.source[1][0],a.source[2][0])});this.f.forEach(function(a){if(Math.max(a.source[0][0],a.source[1][0],a.source[2][0])-n>this.b/2){var b=[[a.source[0][0],a.source[0][1]],[a.source[1][0],a.source[1][1]],[a.source[2][0],a.source[2][1]]];b[0][0]-n>this.b/2&&(b[0][0]-=this.b);b[1][0]-n>this.b/2&&(b[1][0]-=this.b);b[2][0]-n>this.b/2&&(b[2][0]-=this.b);Math.max(b[0][0],b[1][0],b[2][0])-Math.min(b[0][0],b[1][0],b[2][0])<this.b/2&&(a.source=b)}},this)}f={}}
function qk(a,b,c,d,e,f,g,h,l,m){var n=Db([f,g,h,l]),p=a.b?ec(n)/a.b:null,q=a.b,t=a.g.a&&.5<p&&1>p,v=!1;if(0<m){if(a.c.g&&a.l)var u=Db([b,c,d,e]),v=v|.25<ec(u)/a.l;!t&&a.g.g&&p&&(v|=.25<p)}if(v||!a.i||kc(n,a.i)){if(!(v||isFinite(f[0])&&isFinite(f[1])&&isFinite(g[0])&&isFinite(g[1])&&isFinite(h[0])&&isFinite(h[1])&&isFinite(l[0])&&isFinite(l[1])))if(0<m)v=!0;else return;if(0<m&&(v||(n=a.a([(b[0]+d[0])/2,(b[1]+d[1])/2]),q=t?(ta(f[0],q)+ta(h[0],q))/2-ta(n[0],q):(f[0]+h[0])/2-n[0],n=(f[1]+h[1])/2-n[1],
v=q*q+n*n>a.s),v)){Math.abs(b[0]-d[0])<=Math.abs(b[1]-d[1])?(t=[(c[0]+d[0])/2,(c[1]+d[1])/2],q=a.a(t),n=[(e[0]+b[0])/2,(e[1]+b[1])/2],p=a.a(n),qk(a,b,c,t,n,f,g,q,p,m-1),qk(a,n,t,d,e,p,q,h,l,m-1)):(t=[(b[0]+c[0])/2,(b[1]+c[1])/2],q=a.a(t),n=[(d[0]+e[0])/2,(d[1]+e[1])/2],p=a.a(n),qk(a,b,t,n,e,f,q,p,l,m-1),qk(a,t,c,d,n,q,g,h,p,m-1));return}if(t){if(!a.o)return;a.j=!0}a.f.push({source:[f,h,l],target:[b,d,e]});a.f.push({source:[f,g,h],target:[b,c,d]})}}
function rk(a){var b=Eb();a.f.forEach(function(a){a=a.source;Fb(b,a[0]);Fb(b,a[1]);Fb(b,a[2])});return b};function sk(a,b,c,d,e,f){this.v=b;this.s=a.C();var g=b.C(),h=g?jc(c,g):c,g=mk(a,b,gc(h),d);this.j=new pk(a,b,h,this.s,.5*g);this.c=d;this.g=c;a=rk(this.j);this.o=(this.qb=f(a,g,e))?this.qb.f:1;this.zd=this.i=null;e=2;f=[];this.qb&&(e=0,f=this.qb.l);rj.call(this,c,d,this.o,e,f)}w(sk,rj);sk.prototype.ma=function(){1==this.state&&(Ea(this.zd),this.zd=null);rj.prototype.ma.call(this)};sk.prototype.a=function(){return this.i};
sk.prototype.yd=function(){var a=this.qb.V();2==a&&(this.i=ok(ec(this.g)/this.c,fc(this.g)/this.c,this.o,this.qb.$(),0,this.c,this.g,this.j,[{extent:this.qb.C(),image:this.qb.a()}],0));this.state=a;sj(this)};sk.prototype.load=function(){if(0==this.state){this.state=1;sj(this);var a=this.qb.V();2==a||3==a?this.yd():(this.zd=B(this.qb,"change",function(){var a=this.qb.V();if(2==a||3==a)Ea(this.zd),this.zd=null,this.yd()},this),this.qb.load())}};function tk(a){Tg.call(this,{attributions:a.attributions,extent:a.extent,logo:a.logo,projection:a.projection,state:a.state});this.H=void 0!==a.resolutions?a.resolutions:null;this.a=null;this.pa=0}w(tk,Tg);function uk(a,b){a.H&&(b=a.H[cb(a.H,b,0)]);return b}
tk.prototype.A=function(a,b,c,d){var e=this.f;if(e&&d&&!Lc(e,d)){if(this.a){if(this.pa==this.g&&Lc(this.a.v,d)&&this.a.$()==b&&this.a.f==c&&Ub(this.a.C(),a))return this.a;Oa(this.a);this.a=null}this.a=new sk(e,d,a,b,c,function(a,b,c){return this.Kc(a,b,c,e)}.bind(this));this.pa=this.g;return this.a}e&&(d=e);return this.Kc(a,b,c,d)};tk.prototype.o=function(a){a=a.target;switch(a.V()){case 1:this.b(new vk(wk,a));break;case 2:this.b(new vk(xk,a));break;case 3:this.b(new vk(yk,a))}};
function zk(a,b){a.a().src=b}function vk(a,b){Pa.call(this,a);this.image=b}w(vk,Pa);var wk="imageloadstart",xk="imageloadend",yk="imageloaderror";function Ak(a){tk.call(this,{attributions:a.attributions,logo:a.logo,projection:a.projection,resolutions:a.resolutions,state:a.state});this.ha=a.canvasFunction;this.T=null;this.W=0;this.na=void 0!==a.ratio?a.ratio:1.5}w(Ak,tk);Ak.prototype.Kc=function(a,b,c,d){b=uk(this,b);var e=this.T;if(e&&this.W==this.g&&e.$()==b&&e.f==c&&Nb(e.C(),a))return e;a=a.slice();lc(a,this.na);(d=this.ha(a,b,c,[ec(a)/b*c,fc(a)/b*c],d))&&(e=new hk(a,b,c,this.l,d));this.T=e;this.W=this.g;return e};function Bk(a){this.c=a.source;this.Ia=ah();this.i=$e();this.j=[0,0];this.za=void 0==a.renderBuffer?100:a.renderBuffer;this.v=null;Ak.call(this,{attributions:a.attributions,canvasFunction:this.zj.bind(this),logo:a.logo,projection:a.projection,ratio:a.ratio,resolutions:a.resolutions,state:this.c.V()});this.R=null;this.s=void 0;this.uh(a.style);B(this.c,"change",this.$m,this)}w(Bk,Ak);k=Bk.prototype;
k.zj=function(a,b,c,d,e){var f=new Zj(.5*b/c,a,b,this.c.W,this.za);this.c.Nc(a,b,e);var g=!1;this.c.xb(a,function(a){var d;if(!(d=g)){var e;(d=a.hc())?e=d.call(a,b):this.s&&(e=this.s(a,b));if(e){var n,p=!1;Array.isArray(e)||(e=[e]);d=0;for(n=e.length;d<n;++d)p=fk(f,a,e[d],ek(b,c),this.Zm,this)||p;d=p}else d=!1}g=d},this);ak(f);if(g)return null;this.j[0]!=d[0]||this.j[1]!=d[1]?(this.i.canvas.width=d[0],this.i.canvas.height=d[1],this.j[0]=d[0],this.j[1]=d[1]):this.i.clearRect(0,0,d[0],d[1]);a=Ck(this,
gc(a),b,c,d);f.Sa(this.i,c,a,0,{});this.v=f;return this.i.canvas};k.ta=function(a,b,c,d,e){if(this.v){var f={};return this.v.ta(a,b,0,d,function(a){var b=x(a).toString();if(!(b in f))return f[b]=!0,e(a)})}};k.Wm=function(){return this.c};k.Xm=function(){return this.R};k.Ym=function(){return this.s};function Ck(a,b,c,d,e){c=d/c;return jh(a.Ia,e[0]/2,e[1]/2,c,-c,0,-b[0],-b[1])}k.Zm=function(){this.u()};k.$m=function(){Vg(this,this.c.V())};
k.uh=function(a){this.R=void 0!==a?a:cj;this.s=a?aj(this.R):void 0;this.u()};function Dk(a){Dj.call(this,a);this.f=null;this.s=ah();this.c=this.j=null}w(Dk,Dj);Dk.prototype.ta=function(a,b,c,d){var e=this.a;return e.ga().ta(a,b.viewState.resolution,b.viewState.rotation,b.skippedFeatureUids,function(a){return c.call(d,a,e)})};
Dk.prototype.Ac=function(a,b,c,d){if(this.f&&this.f.a())if(this.a.ga()instanceof Bk){if(a=fh(b.pixelToCoordinateTransform,a.slice()),this.ta(a,b,nc,this))return c.call(d,this.a,null)}else if(this.j||(this.j=kh(this.s.slice())),b=fh(this.j,a.slice()),this.c||(this.c=$e(1,1)),this.c.clearRect(0,0,1,1),this.c.drawImage(this.f?this.f.a():null,b[0],b[1],1,1,0,0,1,1),b=this.c.getImageData(0,0,1,1).data,0<b[3])return c.call(d,this.a,b)};
Dk.prototype.l=function(a,b){var c=a.pixelRatio,d=a.viewState,e=d.center,f=d.resolution,g=this.a.ga(),h=a.viewHints,l=a.extent;void 0!==b.extent&&(l=jc(l,b.extent));h[0]||h[1]||dc(l)||(d=g.A(l,f,c,d.projection))&&wj(this,d)&&(this.f=d);if(this.f){var d=this.f,h=d.C(),l=d.$(),m=d.f,f=c*l/(f*m),n=bh(this.s);ih(n,c*a.size[0]/2,c*a.size[1]/2);hh(n,f,f);ih(n,m*(h[0]-e[0])/l,m*(e[1]-h[3])/l);this.j=null;yj(a.attributions,d.l);zj(a,g)}return!!this.f};function Ek(a){Dj.call(this,a);this.c=$e();this.j=null;this.o=Eb();this.R=[0,0,0];this.D=ah();this.H=0}w(Ek,Dj);Ek.prototype.i=function(a,b,c){var d=Hj(this,a,0);Fj(this,"precompose",c,a,d);Fk(this,c,a,b);Gj(this,c,a,d)};
Ek.prototype.l=function(a,b){function c(a){a=a.V();return 2==a||4==a||3==a&&!t}var d=a.pixelRatio,e=a.viewState,f=e.projection,g=this.a,h=g.ga(),l=h.fb(f),m=l.ec(e.resolution,this.H),n=l.$(m),p=e.center;n==e.resolution?(p=Bj(p,n,a.size),e=ic(p,n,e.rotation,a.size)):e=a.extent;void 0!==b.extent&&(e=jc(e,b.extent));if(dc(e))return!1;n=je(l,e,n);p={};p[m]={};var q=this.Pd(h,f,p),t=g.c(),v=Eb(),u=new Vd(0,0,0,0),y,E,z,G;for(z=n.ca;z<=n.ea;++z)for(G=n.fa;G<=n.ia;++G)y=h.dc(m,z,G,d,f),!c(y)&&y.a&&(y=y.a),
c(y)?p[m][y.oa.toString()]=y:(E=he(l,y.oa,q,u,v),E||(y=ie(l,y.oa,u,v))&&q(m+1,y));q=Object.keys(p).map(Number);q.sort(ab);var v=[],J,u=0;for(z=q.length;u<z;++u)for(J in y=q[u],G=p[y],G)y=G[J],2==y.V()&&v.push(y);this.j=v;Aj(a.usedTiles,h,m,n);Cj(a,h,l,d,f,e,m,g.f());xj(a,h);zj(a,h);return!0};
Ek.prototype.Ac=function(a,b,c,d){var e=this.c.canvas,f=b.size,g=b.pixelRatio;e.width=f[0]*g;e.height=f[1]*g;this.i(b,Rg(this.a),this.c);a=this.c.getImageData(a[0],a[1],1,1).data;if(0<a[3])return c.call(d,this.a,a)};
function Fk(a,b,c,d){var e=c.pixelRatio,f=c.viewState,g=f.center,h=f.projection,l=f.rotation,m=c.size,n=Math.round(e*m[0]/2),m=Math.round(e*m[1]/2),f=e/f.resolution,p=a.a,q=p.ga(),t=e*q.Ud(h),v=q.fb(h),p=Ta(p,"render"),u=b,y=1,E,z,G;if(l||p){u=a.c;E=u.canvas;var y=q.zb(e)/e,J=b.canvas.width*y;z=b.canvas.height*y;G=Math.round(Math.sqrt(J*J+z*z));E.width!=G?E.width=E.height=G:u.clearRect(0,0,G,G);E=(G-J)/2/y;z=(G-z)/2/y;f*=y;n=Math.round(y*(n+E));m=Math.round(y*(m+z))}J=u.globalAlpha;u.globalAlpha=
d.opacity;var X=a.j,A,Ma=q.gf(h)&&1==d.opacity;Ma||(X.reverse(),A=[]);var ua=d.extent;if(d=void 0!==ua){var M=bc(ua),oa=ac(ua),Wb=$b(ua),ua=Zb(ua);fh(c.coordinateToPixelTransform,M);fh(c.coordinateToPixelTransform,oa);fh(c.coordinateToPixelTransform,Wb);fh(c.coordinateToPixelTransform,ua);var Ib=E||0,Jb=z||0;u.save();var Yb=u.canvas.width/2,hc=u.canvas.height/2;Oi(u,-l,Yb,hc);u.beginPath();u.moveTo(y*(M[0]*e+Ib),y*(M[1]*e+Jb));u.lineTo(y*(oa[0]*e+Ib),y*(oa[1]*e+Jb));u.lineTo(y*(Wb[0]*e+Ib),y*(Wb[1]*
e+Jb));u.lineTo(y*(ua[0]*e+Ib),y*(ua[1]*e+Jb));u.clip();Oi(u,l,Yb,hc)}M=0;for(oa=X.length;M<oa;++M){var Wb=X[M],ua=Wb.oa,hc=v.Ea(ua,a.o),Yb=ua[0],Pb=Zb(v.Ea(v.pd(g,Yb,a.R))),ua=Math.round(ec(hc)*f),Ib=Math.round(fc(hc)*f),Jb=Math.round((hc[0]-Pb[0])*f/ua)*ua+n+Math.round((Pb[0]-g[0])*f),hc=Math.round((Pb[1]-hc[3])*f/Ib)*Ib+m+Math.round((g[1]-Pb[1])*f);if(!Ma){Pb=[Jb,hc,Jb+ua,hc+Ib];u.save();for(var Od=0,tf=A.length;Od<tf;++Od){var Pd=A[Od];kc(Pb,Pd)&&(u.beginPath(),u.moveTo(Pb[0],Pb[1]),u.lineTo(Pb[0],
Pb[3]),u.lineTo(Pb[2],Pb[3]),u.lineTo(Pb[2],Pb[1]),u.moveTo(Pd[0],Pd[1]),u.lineTo(Pd[2],Pd[1]),u.lineTo(Pd[2],Pd[3]),u.lineTo(Pd[0],Pd[3]),u.closePath(),u.clip())}A.push(Pb)}Yb=q.jf(Yb,e,h);u.drawImage(Wb.ab(),t,t,Yb[0],Yb[1],Jb,hc,ua,Ib);Ma||u.restore()}d&&u.restore();p&&(e=E-n/y+n,h=z-m/y+m,g=jh(a.D,G/2-e,G/2-h,f,-f,-l,-g[0]+e/f,-g[1]-h/f),Fj(a,"render",u,c,g));(l||p)&&b.drawImage(u.canvas,-Math.round(E),-Math.round(z),G/y,G/y);u.globalAlpha=J};function Gk(a){Dj.call(this,a);this.c=!1;this.H=-1;this.A=NaN;this.v=Eb();this.j=this.U=null;this.o=$e()}w(Gk,Dj);
Gk.prototype.i=function(a,b,c){var d=a.extent,e=a.pixelRatio,f=b.Oc?a.skippedFeatureUids:{},g=a.viewState,h=g.projection,g=g.rotation,l=h.C(),m=this.a.ga(),n=Hj(this,a,0);Fj(this,"precompose",c,a,n);var p=b.extent,q=void 0!==p;q&&Ej(c,a,p);if((p=this.j)&&!p.g()){var t=0,v=0,u;if(Ta(this.a,"render")){u=c.canvas.width;var y=c.canvas.height;if(g){var E=Math.round(Math.sqrt(u*u+y*y)),t=(E-u)/2,v=(E-y)/2;u=y=E}this.o.canvas.width=u;this.o.canvas.height=y;u=this.o}else u=c;y=u.globalAlpha;u.globalAlpha=
b.opacity;u!=c&&u.translate(t,v);b=a.size[0]*e;E=a.size[1]*e;Oi(u,-g,b/2,E/2);p.Sa(u,e,n,g,f);if(m.D&&h.a&&!Nb(l,d)){for(var h=d[0],m=ec(l),z=0;h<l[0];)--z,n=m*z,n=Hj(this,a,n),p.Sa(u,e,n,g,f),h+=m;z=0;for(h=d[2];h>l[2];)++z,n=m*z,n=Hj(this,a,n),p.Sa(u,e,n,g,f),h-=m;n=Hj(this,a,0)}Oi(u,g,b/2,E/2);u!=c&&(Fj(this,"render",u,a,n),c.drawImage(u.canvas,-t,-v),u.translate(-t,-v));u.globalAlpha=y}q&&c.restore();Gj(this,c,a,n)};
Gk.prototype.ta=function(a,b,c,d){if(this.j){var e=this.a,f={};return this.j.ta(a,b.viewState.resolution,b.viewState.rotation,{},function(a){var b=x(a).toString();if(!(b in f))return f[b]=!0,c.call(d,a,e)})}};Gk.prototype.D=function(){vj(this)};
Gk.prototype.l=function(a){function b(a){var b,d=a.hc();d?b=d.call(a,m):(d=c.i)&&(b=d(a,m));if(b){if(b){d=!1;if(Array.isArray(b))for(var e=0,f=b.length;e<f;++e)d=fk(q,a,b[e],ek(m,n),this.D,this)||d;else d=fk(q,a,b,ek(m,n),this.D,this)||d;a=d}else a=!1;this.c=this.c||a}}var c=this.a,d=c.ga();yj(a.attributions,d.l);zj(a,d);var e=a.viewHints[0],f=a.viewHints[1],g=c.R,h=c.T;if(!this.c&&!g&&e||!h&&f)return!0;var l=a.extent,h=a.viewState,e=h.projection,m=h.resolution,n=a.pixelRatio,f=c.g,p=c.a,g=ej(c);
void 0===g&&(g=dk);l=Gb(l,p*m);p=h.projection.C();d.D&&h.projection.a&&!Nb(p,a.extent)&&(a=Math.max(ec(l)/2,ec(p)),l[0]=p[0]-a,l[2]=p[2]+a);if(!this.c&&this.A==m&&this.H==f&&this.U==g&&Nb(this.v,l))return!0;this.j=null;this.c=!1;var q=new Zj(.5*m/n,l,m,d.W,c.a);d.Nc(l,m,e);if(g){var t=[];d.xb(l,function(a){t.push(a)},this);t.sort(g);t.forEach(b,this)}else d.xb(l,b,this);ak(q);this.A=m;this.H=f;this.U=g;this.v=l;this.j=q;return!0};var Hk={image:Jj,hybrid:["Polygon","LineString"]},Ik={hybrid:["Image","Text"],vector:Jj};function Jk(a){Ek.call(this,a);this.U=!1;this.v=ah();this.H="vector"==a.s?1:0}w(Jk,Ek);
Jk.prototype.i=function(a,b,c){var d=Hj(this,a,0);Fj(this,"precompose",c,a,d);var e=b.extent,f=void 0!==e;f&&Ej(c,a,e);e=this.a.s;"vector"!==e&&Fk(this,c,a,b);if("image"!==e){var g=this.a,e=Ik[g.s],h=a.pixelRatio,l=b.Oc?a.skippedFeatureUids:{},m=a.viewState,n=m.center,p=m.rotation,q=a.size,m=h/m.resolution,t=g.ga(),v=t.zb(),u=Hj(this,a,0);Ta(g,"render")?(this.c.canvas.width=c.canvas.width,this.c.canvas.height=c.canvas.height,g=this.c):g=c;var y=g.globalAlpha;g.globalAlpha=b.opacity;b=this.j;var t=
t.tileGrid,E,z,G,J,X,A,Ma,ua;z=0;for(G=b.length;z<G;++z)J=b[z],Ma=J.f,X=t.Ea(J.oa,this.o),E=J.oa[0],A="tile-pixels"==J.j.Ab(),E=t.$(E),ua=E/v,E=Math.round(h*q[0]/2),J=Math.round(h*q[1]/2),A?(X=bc(X),bh(this.v),X=jh(this.v,E,J,m*ua,m*ua,p,(X[0]-n[0])/ua,(n[1]-X[1])/ua)):X=u,Oi(g,-p,E,J),Ma.xd.Sa(g,h,X,p,l,e),Oi(g,p,E,J);g!=c&&(Fj(this,"render",g,a,u),c.drawImage(g.canvas,0,0));g.globalAlpha=y}f&&c.restore();Gj(this,c,a,d)};
function Kk(a,b,c){function d(a){var b,c=a.hc();c?b=c.call(a,v):(c=e.i)&&(b=c(a,v));if(b){Array.isArray(b)||(b=[b]);var c=z,d=E;if(b){var f=!1;if(Array.isArray(b))for(var g=0,h=b.length;g<h;++g)f=fk(d,a,b[g],c,this.A,this)||f;else f=fk(d,a,b,c,this.A,this)||f;a=f}else a=!1;this.U=this.U||a;l.gd=l.gd||a}}var e=a.a,f=c.pixelRatio;c=c.viewState.projection;var g=e.g,h=ej(e)||null,l=b.f;if(l.gd||l.Zh!=g||l.Sf!=h){l.xd=null;l.gd=!1;var m=e.ga(),n=m.tileGrid,p=b.oa,q=b.j,t="tile-pixels"==q.Ab(),v=n.$(p[0]),
u;if(t)var y=t=m.zb(),n=ae(n.Qa(p[0])),n=[0,0,n[0]*y,n[1]*y];else t=v,n=n.Ea(p),Lc(c,q)||(u=!0,b.uf(c));l.gd=!1;var E=new Zj(0,n,t,m.i,e.a),z=ek(t,f);b=b.c;h&&h!==l.Sf&&b.sort(h);m=0;for(t=b.length;m<t;++m)f=b[m],u&&f.Y().lb(q,c),d.call(a,f);ak(E);l.Zh=g;l.Sf=h;l.xd=E;l.resolution=NaN}}
Jk.prototype.ta=function(a,b,c,d){var e=b.viewState.resolution;b=b.viewState.rotation;var f=this.a,g={},h=this.j,l=f.ga(),m=l.tileGrid,n,p,q,t,v,u;q=0;for(t=h.length;q<t;++q)u=h[q],p=u.oa,v=l.tileGrid.Ea(p,this.o),Lb(v,a)&&("tile-pixels"===u.j.Ab()?(v=bc(v),e=l.zb(),p=m.$(p[0])/e,p=[(a[0]-v[0])/p,(v[1]-a[1])/p]):p=a,u=u.f.xd,n=n||u.ta(p,e,b,{},function(a){var b=x(a).toString();if(!(b in g))return g[b]=!0,c.call(d,a,f)}));return n};Jk.prototype.A=function(){vj(this)};
Jk.prototype.l=function(a,b){var c=Ek.prototype.l.call(this,a,b);if(c)for(var d=Object.keys(a.De||{}),e=0,f=this.j.length;e<f;++e){var g=this.j[e];Kk(this,g,a);var h=g,g=a,l=this.a,m=Hk[l.s];if(m){var n=g.pixelRatio,p=h.f,q=l.g;if(!hb(p.ri,d)||p.Tf!==q){p.ri=d;p.Tf=q;var q=h.g,t=l.ga(),v=t.tileGrid,u=h.oa[0],y=v.$(u),l=ae(v.Qa(u)),u=v.$(u),E=u/y,z=l[0]*n*E,G=l[1]*n*E;q.canvas.width=z/E+.5;q.canvas.height=G/E+.5;q.scale(1/E,1/E);q.translate(z/2,G/2);E="tile-pixels"==h.j.Ab();y=n/y;t=t.zb();u/=t;v=
v.Ea(h.oa,this.o);h=bh(this.v);E?(hh(h,y*u,y*u),ih(h,-l[0]*t/2,-l[1]*t/2)):(l=gc(v),hh(h,y,-y),ih(h,-l[0],-l[1]));p.xd.Sa(q,n,h,0,g.skippedFeatureUids||{},m)}}}return c};function Lk(a,b){lh.call(this,0,b);this.f=$e();this.b=this.f.canvas;this.b.style.width="100%";this.b.style.height="100%";this.b.className="ol-unselectable";a.insertBefore(this.b,a.childNodes[0]||null);this.a=!0;this.c=ah()}w(Lk,lh);Lk.prototype.Ve=function(a){return a instanceof Ji?new Dk(a):a instanceof Ki?new Ek(a):a instanceof H?new Jk(a):a instanceof F?new Gk(a):null};
function Mk(a,b,c){var d=a.i,e=a.f;if(Ta(d,b)){var f=c.extent,g=c.pixelRatio,h=c.viewState.rotation,l=c.viewState,m=c.pixelRatio/l.resolution;a=jh(a.c,a.b.width/2,a.b.height/2,m,-m,-l.rotation,-l.center[0],-l.center[1]);d.b(new Sg(b,new gj(e,g,f,a,h),c,e,null))}}Lk.prototype.X=function(){return"canvas"};
Lk.prototype.Be=function(a){if(a){var b=this.f,c=a.pixelRatio,d=Math.round(a.size[0]*c),c=Math.round(a.size[1]*c);this.b.width!=d||this.b.height!=c?(this.b.width=d,this.b.height=c):b.clearRect(0,0,d,c);var e=a.viewState.rotation;mh(a);Mk(this,"precompose",a);var f=a.layerStatesArray;ib(f);Oi(b,e,d/2,c/2);var g=a.viewState.resolution,h,l,m,n;h=0;for(l=f.length;h<l;++h)n=f[h],m=n.layer,m=oh(this,m),Xg(n,g)&&"ready"==n.O&&m.l(a,n)&&m.i(a,n,b);Oi(b,-e,d/2,c/2);Mk(this,"postcompose",a);this.a||(this.b.style.display=
"",this.a=!0);ph(this,a);a.postRenderFunctions.push(nh)}else this.a&&(this.b.style.display="none",this.a=!1)};function Nk(a,b){tj.call(this,a);this.target=b}w(Nk,tj);Nk.prototype.Md=ha;Nk.prototype.qh=ha;function Ok(a){var b=document.createElement("DIV");b.style.position="absolute";Nk.call(this,a,b);this.f=null;this.c=ah()}w(Ok,Nk);Ok.prototype.ta=function(a,b,c,d){var e=this.a;return e.ga().ta(a,b.viewState.resolution,b.viewState.rotation,b.skippedFeatureUids,function(a){return c.call(d,a,e)})};Ok.prototype.Md=function(){gf(this.target);this.f=null};
Ok.prototype.xf=function(a,b){var c=a.viewState,d=c.center,e=c.resolution,f=c.rotation,g=this.f,h=this.a.ga(),l=a.viewHints,m=a.extent;void 0!==b.extent&&(m=jc(m,b.extent));l[0]||l[1]||dc(m)||(c=h.A(m,e,a.pixelRatio,c.projection))&&wj(this,c)&&(g=c);g&&(l=g.C(),m=g.$(),c=ah(),jh(c,a.size[0]/2,a.size[1]/2,m/e,m/e,f,(l[0]-d[0])/m,(d[1]-l[3])/m),g!=this.f&&(d=g.a(this),d.style.maxWidth="none",d.style.position="absolute",gf(this.target),this.target.appendChild(d),this.f=g),hb(c,this.c)||(df(this.target,
c),eh(this.c,c)),yj(a.attributions,g.l),zj(a,h));return!0};function Pk(a){var b=document.createElement("DIV");b.style.position="absolute";Nk.call(this,a,b);this.c=!0;this.l=1;this.i=0;this.f={}}w(Pk,Nk);Pk.prototype.Md=function(){gf(this.target);this.i=0};
Pk.prototype.xf=function(a,b){if(!b.visible)return this.c&&(this.target.style.display="none",this.c=!1),!0;var c=a.pixelRatio,d=a.viewState,e=d.projection,f=this.a,g=f.ga(),h=g.fb(e),l=c*g.Ud(e),m=h.ec(d.resolution),n=h.$(m),p=d.center,q;n==d.resolution?(p=Bj(p,n,a.size),q=ic(p,n,d.rotation,a.size)):q=a.extent;void 0!==b.extent&&(q=jc(q,b.extent));var n=je(h,q,n),t={};t[m]={};var v=this.Pd(g,e,t),u=f.c(),y=Eb(),E=new Vd(0,0,0,0),z,G,J,X;for(J=n.ca;J<=n.ea;++J)for(X=n.fa;X<=n.ia;++X)z=g.dc(m,J,X,c,
e),G=z.V(),G=2==G||4==G||3==G&&!u,!G&&z.a&&(z=z.a),G=z.V(),2==G?t[m][z.oa.toString()]=z:4==G||3==G&&!u||(G=he(h,z.oa,v,E,y),G||(z=ie(h,z.oa,E,y))&&v(m+1,z));var A;if(this.i!=g.g){for(A in this.f)u=this.f[+A],ff(u.target);this.f={};this.i=g.g}y=Object.keys(t).map(Number);y.sort(ab);var v={},Ma;J=0;for(X=y.length;J<X;++J){A=y[J];A in this.f?u=this.f[A]:(u=h.pd(p,A),u=new Qk(h,u),v[A]=!0,this.f[A]=u);A=t[A];for(Ma in A){z=u;G=A[Ma];var ua=l,M=G.oa,oa=M[0],Wb=M[1],Ib=M[2],M=M.toString();if(!(M in z.a)){var oa=
ae(z.c.Qa(oa),z.j),Jb=G.ab(z),Yb=Jb.style;Yb.maxWidth="none";var hc,Pb;0<ua?(hc=document.createElement("DIV"),Pb=hc.style,Pb.overflow="hidden",Pb.width=oa[0]+"px",Pb.height=oa[1]+"px",Yb.position="absolute",Yb.left=-ua+"px",Yb.top=-ua+"px",Yb.width=oa[0]+2*ua+"px",Yb.height=oa[1]+2*ua+"px",hc.appendChild(Jb)):(Yb.width=oa[0]+"px",Yb.height=oa[1]+"px",hc=Jb,Pb=Yb);Pb.position="absolute";Pb.left=(Wb-z.g[1])*oa[0]+"px";Pb.top=(z.g[2]-Ib)*oa[1]+"px";z.b||(z.b=document.createDocumentFragment());z.b.appendChild(hc);
z.a[M]=G}}u.b&&(u.target.appendChild(u.b),u.b=null)}l=Object.keys(this.f).map(Number);l.sort(ab);J=ah();Ma=0;for(y=l.length;Ma<y;++Ma)if(A=l[Ma],u=this.f[A],A in t)if(z=u.$(),X=u.Ka(),jh(J,a.size[0]/2,a.size[1]/2,z/d.resolution,z/d.resolution,d.rotation,(X[0]-p[0])/z,(p[1]-X[1])/z),u.setTransform(J),A in v){for(--A;0<=A;--A)if(A in this.f){this.f[A].target.parentNode&&this.f[A].target.parentNode.insertBefore(u.target,this.f[A].target.nextSibling);break}0>A&&this.target.insertBefore(u.target,this.target.childNodes[0]||
null)}else{if(!a.viewHints[0]&&!a.viewHints[1]){G=ge(u.c,q,u.g[0],E);A=[];z=void 0;for(z in u.a)X=u.a[z],ua=X.oa,Wd(G,ua[1],ua[2])||A.push(X);G=0;for(ua=A.length;G<ua;++G)X=A[G],z=X.oa.toString(),ff(X.ab(u)),delete u.a[z]}}else ff(u.target),delete this.f[A];b.opacity!=this.l&&(this.l=this.target.style.opacity=b.opacity);b.visible&&!this.c&&(this.target.style.display="",this.c=!0);Aj(a.usedTiles,g,m,n);Cj(a,g,h,c,e,q,m,f.f());xj(a,g);zj(a,g);return!0};
function Qk(a,b){this.target=document.createElement("DIV");this.target.style.position="absolute";this.target.style.width="100%";this.target.style.height="100%";this.c=a;this.g=b;this.i=bc(a.Ea(b));this.l=a.$(b[0]);this.a={};this.b=null;this.f=ah();this.j=[0,0]}Qk.prototype.Ka=function(){return this.i};Qk.prototype.$=function(){return this.l};Qk.prototype.setTransform=function(a){hb(a,this.f)||(df(this.target,a),eh(this.f,a))};function Rk(a){this.i=$e();var b=this.i.canvas;b.style.maxWidth="none";b.style.position="absolute";Nk.call(this,a,b);this.f=!1;this.l=-1;this.s=NaN;this.j=Eb();this.c=this.o=null;this.U=ah();this.v=ah()}w(Rk,Nk);k=Rk.prototype;k.Md=function(){var a=this.i.canvas;a.width=a.width;this.l=0};
k.qh=function(a,b){var c=a.viewState,d=c.center,e=c.rotation,f=c.resolution,c=a.pixelRatio,g=a.size[0],h=a.size[1],l=g*c,m=h*c,d=jh(this.U,c*g/2,c*h/2,c/f,-c/f,-e,-d[0],-d[1]),f=this.i;f.canvas.width=l;f.canvas.height=m;var n=bh(this.v);hh(n,1/c,1/c);ih(n,-(l-g)/2*c,-(m-h)/2*c);df(f.canvas,n);Sk(this,"precompose",a,d);(g=this.c)&&!g.g()&&(f.globalAlpha=b.opacity,g.Sa(f,c,d,e,b.Oc?a.skippedFeatureUids:{}),Sk(this,"render",a,d));Sk(this,"postcompose",a,d)};
function Sk(a,b,c,d){var e=a.i;a=a.a;Ta(a,b)&&a.b(new Sg(b,new gj(e,c.pixelRatio,c.extent,d,c.viewState.rotation),c,e,null))}k.ta=function(a,b,c,d){if(this.c){var e=this.a,f={};return this.c.ta(a,b.viewState.resolution,b.viewState.rotation,{},function(a){var b=x(a).toString();if(!(b in f))return f[b]=!0,c.call(d,a,e)})}};k.rh=function(){vj(this)};
k.xf=function(a){function b(a){var b,d=a.hc();d?b=d.call(a,l):(d=c.i)&&(b=d(a,l));if(b){if(b){d=!1;if(Array.isArray(b))for(var e=0,f=b.length;e<f;++e)d=fk(n,a,b[e],ek(l,m),this.rh,this)||d;else d=fk(n,a,b,ek(l,m),this.rh,this)||d;a=d}else a=!1;this.f=this.f||a}}var c=this.a,d=c.ga();yj(a.attributions,d.l);zj(a,d);var e=a.viewHints[0],f=a.viewHints[1],g=c.R,h=c.T;if(!this.f&&!g&&e||!h&&f)return!0;var f=a.extent,g=a.viewState,e=g.projection,l=g.resolution,m=a.pixelRatio;a=c.g;h=c.a;g=ej(c);void 0===
g&&(g=dk);f=Gb(f,h*l);if(!this.f&&this.s==l&&this.l==a&&this.o==g&&Nb(this.j,f))return!0;this.c=null;this.f=!1;var n=new Zj(.5*l/m,f,l,d.W,c.a);d.Nc(f,l,e);if(g){var p=[];d.xb(f,function(a){p.push(a)},this);p.sort(g);p.forEach(b,this)}else d.xb(f,b,this);ak(n);this.s=l;this.l=a;this.o=g;this.j=f;this.c=n;return!0};function Tk(a,b){lh.call(this,0,b);this.f=$e();var c=this.f.canvas;c.style.position="absolute";c.style.width="100%";c.style.height="100%";c.className="ol-unselectable";a.insertBefore(c,a.childNodes[0]||null);this.c=ah();this.b=document.createElement("DIV");this.b.className="ol-unselectable";c=this.b.style;c.position="absolute";c.width="100%";c.height="100%";B(this.b,"touchstart",Ra);a.insertBefore(this.b,a.childNodes[0]||null);this.a=!0}w(Tk,lh);Tk.prototype.ma=function(){ff(this.b);lh.prototype.ma.call(this)};
Tk.prototype.Ve=function(a){if(a instanceof Ji)a=new Ok(a);else if(a instanceof Ki)a=new Pk(a);else if(a instanceof F)a=new Rk(a);else return null;return a};function Uk(a,b,c){var d=a.i;if(Ta(d,b)){var e=c.extent,f=c.pixelRatio,g=c.viewState,h=g.rotation,l=a.f,m=l.canvas;a=jh(a.c,m.width/2,m.height/2,f/g.resolution,-f/g.resolution,-h,-g.center[0],-g.center[1]);d.b(new Sg(b,new gj(l,f,e,a,h),c,l,null))}}Tk.prototype.X=function(){return"dom"};
Tk.prototype.Be=function(a){if(a){var b=this.i;if(Ta(b,"precompose")||Ta(b,"postcompose")){var b=this.f.canvas,c=a.pixelRatio;b.width=a.size[0]*c;b.height=a.size[1]*c}Uk(this,"precompose",a);b=a.layerStatesArray;ib(b);var c=a.viewState.resolution,d,e,f,g;d=0;for(e=b.length;d<e;++d)g=b[d],f=g.layer,f=oh(this,f),this.b.insertBefore(f.target,this.b.childNodes[d]||null),Xg(g,c)&&"ready"==g.O?f.xf(a,g)&&f.qh(a,g):f.Md();var b=a.layerStates,h;for(h in this.g)h in b||(f=this.g[h],ff(f.target));this.a||(this.b.style.display=
"",this.a=!0);mh(a);ph(this,a);a.postRenderFunctions.push(nh);Uk(this,"postcompose",a)}else this.a&&(this.b.style.display="none",this.a=!1)};function Vk(a){this.b=a};function Wk(a){this.b=a}w(Wk,Vk);Wk.prototype.X=function(){return 35632};function Xk(a){this.b=a}w(Xk,Vk);Xk.prototype.X=function(){return 35633};function Yk(){this.b="precision mediump float;varying vec2 a;varying float b;uniform float k;uniform sampler2D l;void main(void){vec4 texColor=texture2D(l,a);gl_FragColor.rgb=texColor.rgb;float alpha=texColor.a*b*k;if(alpha==0.0){discard;}gl_FragColor.a=alpha;}"}w(Yk,Wk);var Zk=new Yk;
function $k(){this.b="varying vec2 a;varying float b;attribute vec2 c;attribute vec2 d;attribute vec2 e;attribute float f;attribute float g;uniform mat4 h;uniform mat4 i;uniform mat4 j;void main(void){mat4 offsetMatrix=i;if(g==1.0){offsetMatrix=i*j;}vec4 offsets=offsetMatrix*vec4(e,0.,0.);gl_Position=h*vec4(c,0.,1.)+offsets;a=d;b=f;}"}w($k,Xk);var al=new $k;
function bl(a,b){this.j=a.getUniformLocation(b,"j");this.o=a.getUniformLocation(b,"i");this.i=a.getUniformLocation(b,"k");this.l=a.getUniformLocation(b,"h");this.b=a.getAttribLocation(b,"e");this.a=a.getAttribLocation(b,"f");this.f=a.getAttribLocation(b,"c");this.g=a.getAttribLocation(b,"g");this.c=a.getAttribLocation(b,"d")};function cl(a){this.b=void 0!==a?a:[];this.a=dl}var dl=35044;function el(a,b){this.l=a;this.b=b;this.a={};this.c={};this.f={};this.o=this.s=this.i=this.j=null;(this.g=bb(ga,"OES_element_index_uint"))&&b.getExtension("OES_element_index_uint");B(this.l,"webglcontextlost",this.Yn,this);B(this.l,"webglcontextrestored",this.Zn,this)}w(el,Na);
function fl(a,b,c){var d=a.b,e=c.b,f=String(x(c));if(f in a.a)d.bindBuffer(b,a.a[f].buffer);else{var g=d.createBuffer();d.bindBuffer(b,g);var h;34962==b?h=new Float32Array(e):34963==b&&(h=a.g?new Uint32Array(e):new Uint16Array(e));d.bufferData(b,h,c.a);a.a[f]={Gb:c,buffer:g}}}function gl(a,b){var c=a.b,d=String(x(b)),e=a.a[d];c.isContextLost()||c.deleteBuffer(e.buffer);delete a.a[d]}k=el.prototype;
k.ma=function(){La(this.l);var a=this.b;if(!a.isContextLost()){for(var b in this.a)a.deleteBuffer(this.a[b].buffer);for(b in this.f)a.deleteProgram(this.f[b]);for(b in this.c)a.deleteShader(this.c[b]);a.deleteFramebuffer(this.i);a.deleteRenderbuffer(this.o);a.deleteTexture(this.s)}};k.Xn=function(){return this.b};
function hl(a){if(!a.i){var b=a.b,c=b.createFramebuffer();b.bindFramebuffer(b.FRAMEBUFFER,c);var d=il(b,1,1),e=b.createRenderbuffer();b.bindRenderbuffer(b.RENDERBUFFER,e);b.renderbufferStorage(b.RENDERBUFFER,b.DEPTH_COMPONENT16,1,1);b.framebufferTexture2D(b.FRAMEBUFFER,b.COLOR_ATTACHMENT0,b.TEXTURE_2D,d,0);b.framebufferRenderbuffer(b.FRAMEBUFFER,b.DEPTH_ATTACHMENT,b.RENDERBUFFER,e);b.bindTexture(b.TEXTURE_2D,null);b.bindRenderbuffer(b.RENDERBUFFER,null);b.bindFramebuffer(b.FRAMEBUFFER,null);a.i=c;
a.s=d;a.o=e}return a.i}function jl(a,b){var c=String(x(b));if(c in a.c)return a.c[c];var d=a.b,e=d.createShader(b.X());d.shaderSource(e,b.b);d.compileShader(e);return a.c[c]=e}function kl(a,b,c){var d=x(b)+"/"+x(c);if(d in a.f)return a.f[d];var e=a.b,f=e.createProgram();e.attachShader(f,jl(a,b));e.attachShader(f,jl(a,c));e.linkProgram(f);return a.f[d]=f}k.Yn=function(){Aa(this.a);Aa(this.c);Aa(this.f);this.o=this.s=this.i=this.j=null};k.Zn=function(){};
k.ve=function(a){if(a==this.j)return!1;this.b.useProgram(a);this.j=a;return!0};function ll(a,b,c){var d=a.createTexture();a.bindTexture(a.TEXTURE_2D,d);a.texParameteri(a.TEXTURE_2D,a.TEXTURE_MAG_FILTER,a.LINEAR);a.texParameteri(a.TEXTURE_2D,a.TEXTURE_MIN_FILTER,a.LINEAR);void 0!==b&&a.texParameteri(3553,10242,b);void 0!==c&&a.texParameteri(3553,10243,c);return d}function il(a,b,c){var d=ll(a,void 0,void 0);a.texImage2D(a.TEXTURE_2D,0,a.RGBA,b,c,0,a.RGBA,a.UNSIGNED_BYTE,null);return d}
function ml(a,b){var c=ll(a,33071,33071);a.texImage2D(a.TEXTURE_2D,0,a.RGBA,a.RGBA,a.UNSIGNED_BYTE,b);return c};function nl(a,b){this.H=this.A=void 0;this.o=gc(b);this.U=[];this.i=[];this.O=void 0;this.c=[];this.f=[];this.Ba=this.Aa=void 0;this.a=[];this.D=this.j=null;this.R=void 0;this.za=ah();this.Ha=ah();this.W=this.T=void 0;this.Ia=ah();this.pa=Xe();this.cb=this.ha=this.Va=void 0;this.Lb=[];this.l=[];this.b=[];this.v=null;this.g=[];this.s=[];this.na=void 0}w(nl,fj);
function pl(a,b){var c=a.v,d=a.j,e=a.Lb,f=a.l,g=b.b;return function(){if(!g.isContextLost()){var a,l;a=0;for(l=e.length;a<l;++a)g.deleteTexture(e[a]);a=0;for(l=f.length;a<l;++a)g.deleteTexture(f[a])}gl(b,c);gl(b,d)}}
function ql(a,b,c,d){var e=a.A,f=a.H,g=a.O,h=a.Aa,l=a.Ba,m=a.R,n=a.T,p=a.W,q=a.Va?1:0,t=a.ha,v=a.cb,u=a.na,y=Math.cos(t),t=Math.sin(t),E=a.a.length,z=a.b.length,G,J,X,A,Ma,ua;for(G=0;G<c;G+=d)Ma=b[G]-a.o[0],ua=b[G+1]-a.o[1],J=z/8,X=-v*e,A=-v*(g-f),a.b[z++]=Ma,a.b[z++]=ua,a.b[z++]=X*y-A*t,a.b[z++]=X*t+A*y,a.b[z++]=n/l,a.b[z++]=(p+g)/h,a.b[z++]=m,a.b[z++]=q,X=v*(u-e),A=-v*(g-f),a.b[z++]=Ma,a.b[z++]=ua,a.b[z++]=X*y-A*t,a.b[z++]=X*t+A*y,a.b[z++]=(n+u)/l,a.b[z++]=(p+g)/h,a.b[z++]=m,a.b[z++]=q,X=v*(u-e),
A=v*f,a.b[z++]=Ma,a.b[z++]=ua,a.b[z++]=X*y-A*t,a.b[z++]=X*t+A*y,a.b[z++]=(n+u)/l,a.b[z++]=p/h,a.b[z++]=m,a.b[z++]=q,X=-v*e,A=v*f,a.b[z++]=Ma,a.b[z++]=ua,a.b[z++]=X*y-A*t,a.b[z++]=X*t+A*y,a.b[z++]=n/l,a.b[z++]=p/h,a.b[z++]=m,a.b[z++]=q,a.a[E++]=J,a.a[E++]=J+1,a.a[E++]=J+2,a.a[E++]=J,a.a[E++]=J+2,a.a[E++]=J+3}nl.prototype.tc=function(a,b){this.g.push(this.a.length);this.s.push(b);var c=a.la();ql(this,c,c.length,a.ua())};
nl.prototype.uc=function(a,b){this.g.push(this.a.length);this.s.push(b);var c=a.la();ql(this,c,c.length,a.ua())};function rl(a,b){var c=b.b;a.U.push(a.a.length);a.i.push(a.a.length);a.v=new cl(a.b);fl(b,34962,a.v);a.j=new cl(a.a);fl(b,34963,a.j);var d={};sl(a.Lb,a.c,d,c);sl(a.l,a.f,d,c);a.A=void 0;a.H=void 0;a.O=void 0;a.c=null;a.f=null;a.Aa=void 0;a.Ba=void 0;a.a=null;a.R=void 0;a.T=void 0;a.W=void 0;a.Va=void 0;a.ha=void 0;a.cb=void 0;a.b=null;a.na=void 0}
function sl(a,b,c,d){var e,f,g,h=b.length;for(g=0;g<h;++g)e=b[g],f=x(e).toString(),f in c?e=c[f]:(e=ml(d,e),c[f]=e),a[g]=e}
nl.prototype.Sa=function(a,b,c,d,e,f,g,h,l,m,n){f=a.b;fl(a,34962,this.v);fl(a,34963,this.j);var p=kl(a,Zk,al),q;this.D?q=this.D:this.D=q=new bl(f,p);a.ve(p);f.enableVertexAttribArray(q.f);f.vertexAttribPointer(q.f,2,5126,!1,32,0);f.enableVertexAttribArray(q.b);f.vertexAttribPointer(q.b,2,5126,!1,32,8);f.enableVertexAttribArray(q.c);f.vertexAttribPointer(q.c,2,5126,!1,32,16);f.enableVertexAttribArray(q.a);f.vertexAttribPointer(q.a,1,5126,!1,32,24);f.enableVertexAttribArray(q.g);f.vertexAttribPointer(q.g,
1,5126,!1,32,28);p=bh(this.Ia);hh(p,2/(c*e[0]),2/(c*e[1]));gh(p,-d);ih(p,-(b[0]-this.o[0]),-(b[1]-this.o[1]));b=bh(this.Ha);hh(b,2/e[0],2/e[1]);e=bh(this.za);0!==d&&gh(e,-d);f.uniformMatrix4fv(q.l,!1,Ye(this.pa,p));f.uniformMatrix4fv(q.o,!1,Ye(this.pa,b));f.uniformMatrix4fv(q.j,!1,Ye(this.pa,e));f.uniform1f(q.i,g);var t;if(void 0===l)tl(this,f,a,h,this.Lb,this.U);else{if(m)a:{d=a.g?5125:5123;a=a.g?4:2;e=this.g.length-1;for(g=this.l.length-1;0<=g;--g)for(f.bindTexture(3553,this.l[g]),m=0<g?this.i[g-
1]:0,b=this.i[g];0<=e&&this.g[e]>=m;){t=this.g[e];c=this.s[e];p=x(c).toString();if(void 0===h[p]&&c.Y()&&(void 0===n||kc(n,c.Y().C()))&&(f.clear(f.COLOR_BUFFER_BIT|f.DEPTH_BUFFER_BIT),f.drawElements(4,b-t,d,t*a),b=l(c))){h=b;break a}b=t;e--}h=void 0}else f.clear(f.COLOR_BUFFER_BIT|f.DEPTH_BUFFER_BIT),tl(this,f,a,h,this.l,this.i),h=(h=l(null))?h:void 0;t=h}f.disableVertexAttribArray(q.f);f.disableVertexAttribArray(q.b);f.disableVertexAttribArray(q.c);f.disableVertexAttribArray(q.a);f.disableVertexAttribArray(q.g);
return t};function tl(a,b,c,d,e,f){var g=c.g?5125:5123;c=c.g?4:2;if(Ca(d)){var h;a=0;d=e.length;for(h=0;a<d;++a){b.bindTexture(3553,e[a]);var l=f[a];b.drawElements(4,l-h,g,h*c);h=l}}else{h=0;var m,l=0;for(m=e.length;l<m;++l){b.bindTexture(3553,e[l]);for(var n=0<l?f[l-1]:0,p=f[l],q=n;h<a.g.length&&a.g[h]<=p;){var t=x(a.s[h]).toString();void 0!==d[t]?(q!==n&&b.drawElements(4,n-q,g,q*c),n=q=h===a.g.length-1?p:a.g[h+1]):n=h===a.g.length-1?p:a.g[h+1];h++}q!==n&&b.drawElements(4,n-q,g,q*c)}}}
nl.prototype.Yb=function(a){var b=a.bc(),c=a.mc(1),d=a.kd(),e=a.ne(1),f=a.v,g=a.Ka(),h=a.U,l=a.o,m=a.Jb();a=a.i;var n;0===this.c.length?this.c.push(c):(n=this.c[this.c.length-1],x(n)!=x(c)&&(this.U.push(this.a.length),this.c.push(c)));0===this.f.length?this.f.push(e):(n=this.f[this.f.length-1],x(n)!=x(e)&&(this.i.push(this.a.length),this.f.push(e)));this.A=b[0];this.H=b[1];this.O=m[1];this.Aa=d[1];this.Ba=d[0];this.R=f;this.T=g[0];this.W=g[1];this.ha=l;this.Va=h;this.cb=a;this.na=m[0]};
function ul(a,b,c){this.c=b;this.i=a;this.f=c;this.a={}}w(ul,Ij);function vl(a,b){var c=[],d;for(d in a.a)c.push(pl(a.a[d],b));return function(){for(var a=c.length,b,d=0;d<a;d++)b=c[d].apply(this,arguments);return b}}function wl(a,b){for(var c in a.a)rl(a.a[c],b)}ul.prototype.b=function(a,b){var c=this.a[b];void 0===c&&(c=new xl[b](this.i,this.c),this.a[b]=c);return c};ul.prototype.g=function(){return Ca(this.a)};
ul.prototype.Sa=function(a,b,c,d,e,f,g,h){var l,m,n;l=0;for(m=Jj.length;l<m;++l)n=this.a[Jj[l]],void 0!==n&&n.Sa(a,b,c,d,e,f,g,h,void 0,!1)};function yl(a,b,c,d,e,f,g,h,l,m,n){var p=zl,q,t;for(q=Jj.length-1;0<=q;--q)if(t=a.a[Jj[q]],void 0!==t&&(t=t.Sa(b,c,d,e,p,f,g,h,l,m,n)))return t}
ul.prototype.ta=function(a,b,c,d,e,f,g,h,l,m){var n=b.b;n.bindFramebuffer(n.FRAMEBUFFER,hl(b));var p;void 0!==this.f&&(p=Gb(Rb(a),d*this.f));return yl(this,b,a,d,e,g,h,l,function(a){var b=new Uint8Array(4);n.readPixels(0,0,1,1,n.RGBA,n.UNSIGNED_BYTE,b);if(0<b[3]&&(a=m(a)))return a},!0,p)};
function Al(a,b,c,d,e,f,g,h){var l=c.b;l.bindFramebuffer(l.FRAMEBUFFER,hl(c));return void 0!==yl(a,c,b,d,e,f,g,h,function(){var a=new Uint8Array(4);l.readPixels(0,0,1,1,l.RGBA,l.UNSIGNED_BYTE,a);return 0<a[3]},!1)}var xl={Image:nl},zl=[1,1];function Bl(a,b,c,d,e,f,g){this.b=a;this.f=b;this.g=f;this.c=g;this.j=e;this.l=d;this.i=c;this.a=null}w(Bl,fj);k=Bl.prototype;k.rd=function(a){this.Yb(a.a)};k.sc=function(a){switch(a.X()){case "Point":this.uc(a,null);break;case "MultiPoint":this.tc(a,null);break;case "GeometryCollection":this.Xe(a,null)}};k.We=function(a,b){var c=(0,b.g)(a);c&&kc(this.g,c.C())&&(this.rd(b),this.sc(c))};k.Xe=function(a){a=a.f;var b,c;b=0;for(c=a.length;b<c;++b)this.sc(a[b])};
k.uc=function(a,b){var c=this.b,d=(new ul(1,this.g)).b(0,"Image");d.Yb(this.a);d.uc(a,b);rl(d,c);d.Sa(this.b,this.f,this.i,this.l,this.j,this.c,1,{},void 0,!1);pl(d,c)()};k.tc=function(a,b){var c=this.b,d=(new ul(1,this.g)).b(0,"Image");d.Yb(this.a);d.tc(a,b);rl(d,c);d.Sa(this.b,this.f,this.i,this.l,this.j,this.c,1,{},void 0,!1);pl(d,c)()};k.Yb=function(a){this.a=a};function Cl(){this.b="precision mediump float;varying vec2 a;uniform float f;uniform sampler2D g;void main(void){vec4 texColor=texture2D(g,a);gl_FragColor.rgb=texColor.rgb;gl_FragColor.a=texColor.a*f;}"}w(Cl,Wk);var Dl=new Cl;function El(){this.b="varying vec2 a;attribute vec2 b;attribute vec2 c;uniform mat4 d;uniform mat4 e;void main(void){gl_Position=e*vec4(b,0.,1.);a=(d*vec4(c,0.,1.)).st;}"}w(El,Xk);var Fl=new El;
function Gl(a,b){this.g=a.getUniformLocation(b,"f");this.f=a.getUniformLocation(b,"e");this.i=a.getUniformLocation(b,"d");this.c=a.getUniformLocation(b,"g");this.b=a.getAttribLocation(b,"b");this.a=a.getAttribLocation(b,"c")};function Hl(a,b){tj.call(this,b);this.f=a;this.T=new cl([-1,-1,0,0,1,-1,1,0,-1,1,0,1,1,1,1,1]);this.i=this.rb=null;this.l=void 0;this.s=ah();this.U=ah();this.H=Xe();this.v=null}w(Hl,tj);
function Il(a,b,c){var d=a.f.f;if(void 0===a.l||a.l!=c){b.postRenderFunctions.push(function(a,b,c){a.isContextLost()||(a.deleteFramebuffer(b),a.deleteTexture(c))}.bind(null,d,a.i,a.rb));b=il(d,c,c);var e=d.createFramebuffer();d.bindFramebuffer(36160,e);d.framebufferTexture2D(36160,36064,3553,b,0);a.rb=b;a.i=e;a.l=c}else d.bindFramebuffer(36160,a.i)}
Hl.prototype.sh=function(a,b,c){Jl(this,"precompose",c,a);fl(c,34962,this.T);var d=c.b,e=kl(c,Dl,Fl),f;this.v?f=this.v:this.v=f=new Gl(d,e);c.ve(e)&&(d.enableVertexAttribArray(f.b),d.vertexAttribPointer(f.b,2,5126,!1,16,0),d.enableVertexAttribArray(f.a),d.vertexAttribPointer(f.a,2,5126,!1,16,8),d.uniform1i(f.c,0));d.uniformMatrix4fv(f.i,!1,Ye(this.H,this.s));d.uniformMatrix4fv(f.f,!1,Ye(this.H,this.U));d.uniform1f(f.g,b.opacity);d.bindTexture(3553,this.rb);d.drawArrays(5,0,4);Jl(this,"postcompose",
c,a)};function Jl(a,b,c,d){a=a.a;if(Ta(a,b)){var e=d.viewState;a.b(new Sg(b,new Bl(c,e.center,e.resolution,e.rotation,d.size,d.extent,d.pixelRatio),d,null,c))}}Hl.prototype.yf=function(){this.i=this.rb=null;this.l=void 0};function Kl(a,b){Hl.call(this,a,b);this.o=this.j=this.c=null}w(Kl,Hl);function Ll(a,b){var c=b.a();return ml(a.f.f,c)}Kl.prototype.ta=function(a,b,c,d){var e=this.a;return e.ga().ta(a,b.viewState.resolution,b.viewState.rotation,b.skippedFeatureUids,function(a){return c.call(d,a,e)})};
Kl.prototype.zf=function(a,b){var c=this.f.f,d=a.pixelRatio,e=a.viewState,f=e.center,g=e.resolution,h=e.rotation,l=this.c,m=this.rb,n=this.a.ga(),p=a.viewHints,q=a.extent;void 0!==b.extent&&(q=jc(q,b.extent));p[0]||p[1]||dc(q)||(e=n.A(q,g,d,e.projection))&&wj(this,e)&&(l=e,m=Ll(this,e),this.rb&&a.postRenderFunctions.push(function(a,b){a.isContextLost()||a.deleteTexture(b)}.bind(null,c,this.rb)));l&&(c=this.f.c.l,Ml(this,c.width,c.height,d,f,g,h,l.C()),this.o=null,d=this.s,bh(d),hh(d,1,-1),ih(d,0,
-1),this.c=l,this.rb=m,yj(a.attributions,l.l),zj(a,n));return!0};function Ml(a,b,c,d,e,f,g,h){b*=f;c*=f;a=a.U;bh(a);hh(a,2*d/b,2*d/c);gh(a,-g);ih(a,h[0]-e[0],h[1]-e[1]);hh(a,(h[2]-h[0])/2,(h[3]-h[1])/2);ih(a,1,1)}Kl.prototype.je=function(a,b){return void 0!==this.ta(a,b,nc,this)};
Kl.prototype.Ac=function(a,b,c,d){if(this.c&&this.c.a())if(this.a.ga()instanceof Bk){var e=fh(b.pixelToCoordinateTransform,a.slice());if(this.ta(e,b,nc,this))return c.call(d,this.a,null)}else{e=[this.c.a().width,this.c.a().height];if(!this.o){var f=b.size;b=ah();ih(b,-1,-1);hh(b,2/f[0],2/f[1]);ih(b,0,f[1]);hh(b,1,-1);var f=kh(this.U.slice()),g=ah();ih(g,0,e[1]);hh(g,1,-1);hh(g,e[0]/2,e[1]/2);ih(g,1,1);dh(g,f);dh(g,b);this.o=g}a=fh(this.o,a.slice());if(!(0>a[0]||a[0]>e[0]||0>a[1]||a[1]>e[1])&&(this.j||
(this.j=$e(1,1)),this.j.clearRect(0,0,1,1),this.j.drawImage(this.c.a(),a[0],a[1],1,1,0,0,1,1),e=this.j.getImageData(0,0,1,1).data,0<e[3]))return c.call(d,this.a,e)}};function Nl(){this.b="precision mediump float;varying vec2 a;uniform sampler2D e;void main(void){gl_FragColor=texture2D(e,a);}"}w(Nl,Wk);var Ol=new Nl;function Pl(){this.b="varying vec2 a;attribute vec2 b;attribute vec2 c;uniform vec4 d;void main(void){gl_Position=vec4(b*d.xy+d.zw,0.,1.);a=c;}"}w(Pl,Xk);var Ql=new Pl;function Rl(a,b){this.g=a.getUniformLocation(b,"e");this.f=a.getUniformLocation(b,"d");this.b=a.getAttribLocation(b,"b");this.a=a.getAttribLocation(b,"c")};function Sl(a,b){Hl.call(this,a,b);this.O=Ol;this.W=Ql;this.c=null;this.D=new cl([0,0,0,1,1,0,1,1,0,1,0,0,1,1,1,0]);this.A=this.j=null;this.o=-1;this.R=[0,0]}w(Sl,Hl);k=Sl.prototype;k.ma=function(){gl(this.f.c,this.D);Hl.prototype.ma.call(this)};k.Pd=function(a,b,c){var d=this.f;return function(e,f){return uj(a,b,e,f,function(a){var b=d.a.b.hasOwnProperty(a.Ya());b&&(c[e]||(c[e]={}),c[e][a.oa.toString()]=a);return b})}};k.yf=function(){Hl.prototype.yf.call(this);this.c=null};
k.zf=function(a,b,c){var d=this.f,e=c.b,f=a.viewState,g=f.projection,h=this.a,l=h.ga(),m=l.fb(g),n=m.ec(f.resolution),p=m.$(n),q=l.jf(n,a.pixelRatio,g),t=q[0]/ae(m.Qa(n),this.R)[0],v=p/t,u=a.pixelRatio*l.Ud(g),y=f.center,E;p==f.resolution?(y=Bj(y,p,a.size),E=ic(y,p,f.rotation,a.size)):E=a.extent;p=je(m,E,p);if(this.j&&Xd(this.j,p)&&this.o==l.g)v=this.A;else{var z=[p.ea-p.ca+1,p.ia-p.fa+1],G=pa(Math.max(z[0]*q[0],z[1]*q[1])),z=v*G,J=m.Ka(n),X=J[0]+p.ca*q[0]*v,v=J[1]+p.fa*q[1]*v,v=[X,v,X+z,v+z];Il(this,
a,G);e.viewport(0,0,G,G);e.clearColor(0,0,0,0);e.clear(16384);e.disable(3042);G=kl(c,this.O,this.W);c.ve(G);this.c||(this.c=new Rl(e,G));fl(c,34962,this.D);e.enableVertexAttribArray(this.c.b);e.vertexAttribPointer(this.c.b,2,5126,!1,16,0);e.enableVertexAttribArray(this.c.a);e.vertexAttribPointer(this.c.a,2,5126,!1,16,8);e.uniform1i(this.c.g,0);c={};c[n]={};var A=this.Pd(l,g,c),Ma=h.c(),G=!0,X=Eb(),ua=new Vd(0,0,0,0),M,oa,Wb;for(oa=p.ca;oa<=p.ea;++oa)for(Wb=p.fa;Wb<=p.ia;++Wb){J=l.dc(n,oa,Wb,t,g);
if(void 0!==b.extent&&(M=m.Ea(J.oa,X),!kc(M,b.extent)))continue;M=J.V();M=2==M||4==M||3==M&&!Ma;!M&&J.a&&(J=J.a);M=J.V();if(2==M){if(d.a.b.hasOwnProperty(J.Ya())){c[n][J.oa.toString()]=J;continue}}else if(4==M||3==M&&!Ma)continue;G=!1;M=he(m,J.oa,A,ua,X);M||(J=ie(m,J.oa,ua,X))&&A(n+1,J)}b=Object.keys(c).map(Number);b.sort(ab);for(var A=new Float32Array(4),Ib,Ma=0,ua=b.length;Ma<ua;++Ma)for(Ib in oa=c[b[Ma]],oa)J=oa[Ib],M=m.Ea(J.oa,X),A[0]=2*(M[2]-M[0])/z,A[1]=2*(M[3]-M[1])/z,A[2]=2*(M[0]-v[0])/z-
1,A[3]=2*(M[1]-v[1])/z-1,e.uniform4fv(this.c.f,A),Tl(d,J,q,u*t),e.drawArrays(5,0,4);G?(this.j=p,this.A=v,this.o=l.g):(this.A=this.j=null,this.o=-1,a.animate=!0)}Aj(a.usedTiles,l,n,p);var Jb=d.j;Cj(a,l,m,t,g,E,n,h.f(),function(a){2!=a.V()||d.a.b.hasOwnProperty(a.Ya())||a.Ya()in Jb.a||Jb.c([a,le(m,a.oa),m.$(a.oa[0]),q,u*t])},this);xj(a,l);zj(a,l);e=this.s;bh(e);ih(e,(y[0]-v[0])/(v[2]-v[0]),(y[1]-v[1])/(v[3]-v[1]));0!==f.rotation&&gh(e,f.rotation);hh(e,a.size[0]*f.resolution/(v[2]-v[0]),a.size[1]*f.resolution/
(v[3]-v[1]));ih(e,-.5,-.5);return!0};k.Ac=function(a,b,c,d){if(this.i){a=fh(this.s,[a[0]/b.size[0],(b.size[1]-a[1])/b.size[1]].slice());a=[a[0]*this.l,a[1]*this.l];b=this.f.c.b;b.bindFramebuffer(b.FRAMEBUFFER,this.i);var e=new Uint8Array(4);b.readPixels(a[0],a[1],1,1,b.RGBA,b.UNSIGNED_BYTE,e);if(0<e[3])return c.call(d,this.a,e)}};function Ul(a,b){Hl.call(this,a,b);this.o=!1;this.R=-1;this.O=NaN;this.A=Eb();this.j=this.c=this.D=null}w(Ul,Hl);k=Ul.prototype;k.sh=function(a,b,c){this.j=b;var d=a.viewState,e=this.c;e&&!e.g()&&e.Sa(c,d.center,d.resolution,d.rotation,a.size,a.pixelRatio,b.opacity,b.Oc?a.skippedFeatureUids:{})};k.ma=function(){var a=this.c;a&&(vl(a,this.f.c)(),this.c=null);Hl.prototype.ma.call(this)};
k.ta=function(a,b,c,d){if(this.c&&this.j){var e=b.viewState,f=this.a,g={};return this.c.ta(a,this.f.c,e.center,e.resolution,e.rotation,b.size,b.pixelRatio,this.j.opacity,{},function(a){var b=x(a).toString();if(!(b in g))return g[b]=!0,c.call(d,a,f)})}};k.je=function(a,b){if(this.c&&this.j){var c=b.viewState;return Al(this.c,a,this.f.c,c.resolution,c.rotation,b.pixelRatio,this.j.opacity,b.skippedFeatureUids)}return!1};
k.Ac=function(a,b,c,d){a=fh(b.pixelToCoordinateTransform,a.slice());if(this.je(a,b))return c.call(d,this.a,null)};k.th=function(){vj(this)};
k.zf=function(a,b,c){function d(a){var b,c=a.hc();c?b=c.call(a,m):(c=e.i)&&(b=c(a,m));if(b){if(b){c=!1;if(Array.isArray(b))for(var d=0,f=b.length;d<f;++d)c=fk(q,a,b[d],ek(m,n),this.th,this)||c;else c=fk(q,a,b,ek(m,n),this.th,this)||c;a=c}else a=!1;this.o=this.o||a}}var e=this.a;b=e.ga();yj(a.attributions,b.l);zj(a,b);var f=a.viewHints[0],g=a.viewHints[1],h=e.R,l=e.T;if(!this.o&&!h&&f||!l&&g)return!0;var g=a.extent,h=a.viewState,f=h.projection,m=h.resolution,n=a.pixelRatio,h=e.g,p=e.a,l=ej(e);void 0===
l&&(l=dk);g=Gb(g,p*m);if(!this.o&&this.O==m&&this.R==h&&this.D==l&&Nb(this.A,g))return!0;this.c&&a.postRenderFunctions.push(vl(this.c,c));this.o=!1;var q=new ul(.5*m/n,g,e.a);b.Nc(g,m,f);if(l){var t=[];b.xb(g,function(a){t.push(a)},this);t.sort(l);t.forEach(d,this)}else b.xb(g,d,this);wl(q,c);this.O=m;this.R=h;this.D=l;this.A=g;this.c=q;return!0};function Vl(){this.f=0;this.b={};this.g=this.a=null}k=Vl.prototype;k.clear=function(){this.f=0;this.b={};this.g=this.a=null};k.forEach=function(a,b){for(var c=this.a;c;)a.call(b,c.Fc,c.fc,this),c=c.Cb};k.get=function(a){a=this.b[a];la(void 0!==a,15);if(a===this.g)return a.Fc;a===this.a?(this.a=this.a.Cb,this.a.Sc=null):(a.Cb.Sc=a.Sc,a.Sc.Cb=a.Cb);a.Cb=null;a.Sc=this.g;this.g=this.g.Cb=a;return a.Fc};
k.pop=function(){var a=this.a;delete this.b[a.fc];a.Cb&&(a.Cb.Sc=null);this.a=a.Cb;this.a||(this.g=null);--this.f;return a.Fc};k.replace=function(a,b){this.get(a);this.b[a].Fc=b};k.set=function(a,b){la(!(a in this.b),16);var c={fc:a,Cb:null,Sc:this.g,Fc:b};this.g?this.g.Cb=c:this.a=c;this.g=c;this.b[a]=c;++this.f};function Wl(a,b){lh.call(this,0,b);this.b=document.createElement("CANVAS");this.b.style.width="100%";this.b.style.height="100%";this.b.className="ol-unselectable";a.insertBefore(this.b,a.childNodes[0]||null);this.U=this.A=0;this.H=$e();this.o=!0;this.f=Ef(this.b,{antialias:!0,depth:!1,failIfMajorPerformanceCaveat:!0,preserveDrawingBuffer:!1,stencil:!0});this.c=new el(this.b,this.f);B(this.b,"webglcontextlost",this.Km,this);B(this.b,"webglcontextrestored",this.Lm,this);this.a=new Vl;this.v=null;this.j=
new sh(function(a){var b=a[1];a=a[2];var e=b[0]-this.v[0],b=b[1]-this.v[1];return 65536*Math.log(a)+Math.sqrt(e*e+b*b)/a}.bind(this),function(a){return a[0].Ya()});this.D=function(){if(0!==this.j.b.length){wh(this.j);var a=th(this.j);Tl(this,a[0],a[3],a[4])}return!1}.bind(this);this.l=0;Xl(this)}w(Wl,lh);
function Tl(a,b,c,d){var e=a.f,f=b.Ya();if(a.a.b.hasOwnProperty(f))a=a.a.get(f),e.bindTexture(3553,a.rb),9729!=a.Tg&&(e.texParameteri(3553,10240,9729),a.Tg=9729),9729!=a.Vg&&(e.texParameteri(3553,10241,9729),a.Vg=9729);else{var g=e.createTexture();e.bindTexture(3553,g);if(0<d){var h=a.H.canvas,l=a.H;a.A!==c[0]||a.U!==c[1]?(h.width=c[0],h.height=c[1],a.A=c[0],a.U=c[1]):l.clearRect(0,0,c[0],c[1]);l.drawImage(b.ab(),d,d,c[0],c[1],0,0,c[0],c[1]);e.texImage2D(3553,0,6408,6408,5121,h)}else e.texImage2D(3553,
0,6408,6408,5121,b.ab());e.texParameteri(3553,10240,9729);e.texParameteri(3553,10241,9729);e.texParameteri(3553,10242,33071);e.texParameteri(3553,10243,33071);a.a.set(f,{rb:g,Tg:9729,Vg:9729})}}k=Wl.prototype;k.Ve=function(a){return a instanceof Ji?new Kl(this,a):a instanceof Ki?new Sl(this,a):a instanceof F?new Ul(this,a):null};function Yl(a,b,c){var d=a.i;if(Ta(d,b)){a=a.c;var e=c.viewState;d.b(new Sg(b,new Bl(a,e.center,e.resolution,e.rotation,c.size,c.extent,c.pixelRatio),c,null,a))}}
k.ma=function(){var a=this.f;a.isContextLost()||this.a.forEach(function(b){b&&a.deleteTexture(b.rb)});Oa(this.c);lh.prototype.ma.call(this)};k.Dj=function(a,b){for(var c=this.f,d;1024<this.a.f-this.l;){if(d=this.a.a.Fc)c.deleteTexture(d.rb);else if(+this.a.a.fc==b.index)break;else--this.l;this.a.pop()}};k.X=function(){return"webgl"};k.Km=function(a){a.preventDefault();this.a.clear();this.l=0;a=this.g;for(var b in a)a[b].yf()};k.Lm=function(){Xl(this);this.i.render()};
function Xl(a){a=a.f;a.activeTexture(33984);a.blendFuncSeparate(770,771,1,771);a.disable(2884);a.disable(2929);a.disable(3089);a.disable(2960)}
k.Be=function(a){var b=this.c,c=this.f;if(c.isContextLost())return!1;if(!a)return this.o&&(this.b.style.display="none",this.o=!1),!1;this.v=a.focus;this.a.set((-a.index).toString(),null);++this.l;Yl(this,"precompose",a);var d=[],e=a.layerStatesArray;ib(e);var f=a.viewState.resolution,g,h,l,m;g=0;for(h=e.length;g<h;++g)m=e[g],Xg(m,f)&&"ready"==m.O&&(l=oh(this,m.layer),l.zf(a,m,b)&&d.push(m));e=a.size[0]*a.pixelRatio;f=a.size[1]*a.pixelRatio;if(this.b.width!=e||this.b.height!=f)this.b.width=e,this.b.height=
f;c.bindFramebuffer(36160,null);c.clearColor(0,0,0,0);c.clear(16384);c.enable(3042);c.viewport(0,0,this.b.width,this.b.height);g=0;for(h=d.length;g<h;++g)m=d[g],l=oh(this,m.layer),l.sh(a,m,b);this.o||(this.b.style.display="",this.o=!0);mh(a);1024<this.a.f-this.l&&a.postRenderFunctions.push(this.Dj.bind(this));0!==this.j.b.length&&(a.postRenderFunctions.push(this.D),a.animate=!0);Yl(this,"postcompose",a);ph(this,a);a.postRenderFunctions.push(nh)};
k.ta=function(a,b,c,d,e,f){var g;if(this.f.isContextLost())return!1;var h=b.viewState,l=b.layerStatesArray,m;for(m=l.length-1;0<=m;--m){g=l[m];var n=g.layer;if(Xg(g,h.resolution)&&e.call(f,n)&&(g=oh(this,n).ta(a,b,c,d)))return g}};k.ph=function(a,b,c,d){var e=!1;if(this.f.isContextLost())return!1;var f=b.viewState,g=b.layerStatesArray,h;for(h=g.length-1;0<=h;--h){var l=g[h],m=l.layer;if(Xg(l,f.resolution)&&c.call(d,m)&&(e=oh(this,m).je(a,b)))return!0}return e};
k.oh=function(a,b,c,d,e){if(this.f.isContextLost())return!1;var f=b.viewState,g,h=b.layerStatesArray,l;for(l=h.length-1;0<=l;--l){g=h[l];var m=g.layer;if(Xg(g,f.resolution)&&e.call(d,m)&&(g=oh(this,m).Ac(a,b,c,d)))return g}};var Zl=["canvas","webgl","dom"];
function I(a){Xa.call(this);var b=$l(a);this.tb=void 0!==a.loadTilesWhileAnimating?a.loadTilesWhileAnimating:!1;this.Mb=void 0!==a.loadTilesWhileInteracting?a.loadTilesWhileInteracting:!1;this.Ne=void 0!==a.pixelRatio?a.pixelRatio:Kf;this.Me=b.logos;this.W=function(){this.i=void 0;this.Ro.call(this,Date.now())}.bind(this);this.Ia=ah();this.Ke=ah();this.sb=0;this.f=null;this.Ha=Eb();this.D=this.R=null;this.a=document.createElement("DIV");this.a.className="ol-viewport"+(Pf?" ol-touch":"");this.a.style.position=
"relative";this.a.style.overflow="hidden";this.a.style.width="100%";this.a.style.height="100%";this.a.style.msTouchAction="none";this.a.style.touchAction="none";this.A=document.createElement("DIV");this.A.className="ol-overlaycontainer";this.a.appendChild(this.A);this.v=document.createElement("DIV");this.v.className="ol-overlaycontainer-stopevent";a=["click","dblclick","mousedown","touchstart","mspointerdown",Mg,"mousewheel","wheel"];for(var c=0,d=a.length;c<d;++c)B(this.v,a[c],Qa);this.a.appendChild(this.v);
this.pa=new Eg(this);for(var e in Pg)B(this.pa,Pg[e],this.Ng,this);this.ha=b.keyboardEventTarget;this.s=null;B(this.a,"wheel",this.Mc,this);B(this.a,"mousewheel",this.Mc,this);this.j=b.controls;this.l=b.interactions;this.o=b.overlays;this.Bf={};this.H=new b.To(this.a,this);this.T=null;this.O=[];this.za=[];this.na=new xh(this.yk.bind(this),this.cl.bind(this));this.De={};B(this,Za("layergroup"),this.Lk,this);B(this,Za("view"),this.dl,this);B(this,Za("size"),this.$k,this);B(this,Za("target"),this.bl,
this);this.G(b.values);this.j.forEach(function(a){a.setMap(this)},this);B(this.j,ue,function(a){a.element.setMap(this)},this);B(this.j,ve,function(a){a.element.setMap(null)},this);this.l.forEach(function(a){a.setMap(this)},this);B(this.l,ue,function(a){a.element.setMap(this)},this);B(this.l,ve,function(a){a.element.setMap(null)},this);this.o.forEach(this.lg,this);B(this.o,ue,function(a){this.lg(a.element)},this);B(this.o,ve,function(a){var b=a.element.Oa();void 0!==b&&delete this.Bf[b.toString()];
a.element.setMap(null)},this)}w(I,Xa);k=I.prototype;k.qj=function(a){this.j.push(a)};k.rj=function(a){this.l.push(a)};k.jg=function(a){this.wc().Rc().push(a)};k.kg=function(a){this.o.push(a)};k.lg=function(a){var b=a.Oa();void 0!==b&&(this.Bf[b.toString()]=a);a.setMap(this)};k.$a=function(a){this.render();Array.prototype.push.apply(this.O,arguments)};
k.ma=function(){Oa(this.pa);Oa(this.H);Ka(this.a,"wheel",this.Mc,this);Ka(this.a,"mousewheel",this.Mc,this);void 0!==this.c&&(ja.removeEventListener("resize",this.c,!1),this.c=void 0);this.i&&(ja.cancelAnimationFrame(this.i),this.i=void 0);this.bh(null);Xa.prototype.ma.call(this)};k.Rd=function(a,b,c,d,e){if(this.f)return a=this.Na(a),this.H.ta(a,this.f,b,void 0!==c?c:null,void 0!==d?d:nc,void 0!==e?e:null)};
k.Ol=function(a,b,c,d,e){if(this.f)return this.H.oh(a,this.f,b,void 0!==c?c:null,void 0!==d?d:nc,void 0!==e?e:null)};k.fl=function(a,b,c){if(!this.f)return!1;a=this.Na(a);return this.H.ph(a,this.f,void 0!==b?b:nc,void 0!==c?c:null)};k.Tj=function(a){return this.Na(this.Td(a))};k.Td=function(a){var b=this.a.getBoundingClientRect();a=a.changedTouches?a.changedTouches[0]:a;return[a.clientX-b.left,a.clientY-b.top]};k.sf=function(){return this.get("target")};
k.xc=function(){var a=this.sf();return void 0!==a?"string"===typeof a?document.getElementById(a):a:null};k.Na=function(a){var b=this.f;return b?fh(b.pixelToCoordinateTransform,a.slice()):null};k.Rj=function(){return this.j};k.kk=function(){return this.o};k.jk=function(a){a=this.Bf[a.toString()];return void 0!==a?a:null};k.Yj=function(){return this.l};k.wc=function(){return this.get("layergroup")};k.ah=function(){return this.wc().Rc()};
k.Ga=function(a){var b=this.f;return b?fh(b.coordinateToPixelTransform,a.slice(0,2)):null};k.kb=function(){return this.get("size")};k.aa=function(){return this.get("view")};k.Ak=function(){return this.a};k.yk=function(a,b,c,d){var e=this.f;if(!(e&&b in e.wantedTiles&&e.wantedTiles[b][a.Ya()]))return Infinity;a=c[0]-e.focus[0];c=c[1]-e.focus[1];return 65536*Math.log(d)+Math.sqrt(a*a+c*c)/d};k.Mc=function(a,b){var c=new Cg(b||a.type,this,a);this.Ng(c)};
k.Ng=function(a){if(this.f){this.T=a.coordinate;a.frameState=this.f;var b=this.l.a,c;if(!1!==this.b(a))for(c=b.length-1;0<=c;c--){var d=b[c];if(d.f()&&!d.handleEvent(a))break}}};k.Yk=function(){var a=this.f,b=this.na;if(0!==b.b.length){var c=16,d=c;if(a){var e=a.viewHints;e[0]&&(c=this.tb?8:0,d=2);e[1]&&(c=this.Mb?8:0,d=2)}b.l<c&&(wh(b),yh(b,c,d))}b=this.za;c=0;for(d=b.length;c<d;++c)b[c](this,a);b.length=0};k.$k=function(){this.render()};
k.bl=function(){var a;this.sf()&&(a=this.xc());if(this.s){for(var b=0,c=this.s.length;b<c;++b)Ea(this.s[b]);this.s=null}a?(a.appendChild(this.a),a=this.ha?this.ha:a,this.s=[B(a,"keydown",this.Mc,this),B(a,"keypress",this.Mc,this)],this.c||(this.c=this.Wc.bind(this),ja.addEventListener("resize",this.c,!1))):(ff(this.a),void 0!==this.c&&(ja.removeEventListener("resize",this.c,!1),this.c=void 0));this.Wc()};k.cl=function(){this.render()};k.el=function(){this.render()};
k.dl=function(){this.R&&(Ea(this.R),this.R=null);var a=this.aa();a&&(this.R=B(a,"propertychange",this.el,this));this.render()};k.Lk=function(){this.D&&(this.D.forEach(Ea),this.D=null);var a=this.wc();a&&(this.D=[B(a,"propertychange",this.render,this),B(a,"change",this.render,this)]);this.render()};k.So=function(){this.i&&ja.cancelAnimationFrame(this.i);this.W()};k.render=function(){void 0===this.i&&(this.i=ja.requestAnimationFrame(this.W))};k.Lo=function(a){return this.j.remove(a)};k.Mo=function(a){return this.l.remove(a)};
k.Oo=function(a){return this.wc().Rc().remove(a)};k.Po=function(a){return this.o.remove(a)};
k.Ro=function(a){var b,c,d,e=this.kb(),f=this.aa(),g=Eb(),h=null;if(void 0!==e&&0<e[0]&&0<e[1]&&f&&Jd(f)){var h=Fd(f,this.f?this.f.viewHints:void 0),l=this.wc().ff(),m={};b=0;for(c=l.length;b<c;++b)m[x(l[b].layer)]=l[b];d=f.V();h={animate:!1,attributions:{},coordinateToPixelTransform:this.Ia,extent:g,focus:this.T?this.T:d.center,index:this.sb++,layerStates:m,layerStatesArray:l,logos:za({},this.Me),pixelRatio:this.Ne,pixelToCoordinateTransform:this.Ke,postRenderFunctions:[],size:e,skippedFeatureUids:this.De,
tileQueue:this.na,time:a,usedTiles:{},viewState:d,viewHints:h,wantedTiles:{}}}if(h){a=this.O;b=e=0;for(c=a.length;b<c;++b)f=a[b],f(this,h)&&(a[e++]=f);a.length=e;h.extent=ic(d.center,d.resolution,d.rotation,h.size,g)}this.f=h;this.H.Be(h);h&&(h.animate&&this.render(),Array.prototype.push.apply(this.za,h.postRenderFunctions),0!==this.O.length||h.viewHints[0]||h.viewHints[1]||Ub(h.extent,this.Ha)||(this.b(new hf("moveend",this,h)),Hb(h.extent,this.Ha)));this.b(new hf("postrender",this,h));Af(this.Yk,
this)};k.gi=function(a){this.set("layergroup",a)};k.Vf=function(a){this.set("size",a)};k.bh=function(a){this.set("target",a)};k.hp=function(a){this.set("view",a)};k.pi=function(a){a=x(a).toString();this.De[a]=!0;this.render()};
k.Wc=function(){var a=this.xc();if(a){var b=ja.getComputedStyle(a);this.Vf([a.offsetWidth-parseFloat(b.borderLeftWidth)-parseFloat(b.paddingLeft)-parseFloat(b.paddingRight)-parseFloat(b.borderRightWidth),a.offsetHeight-parseFloat(b.borderTopWidth)-parseFloat(b.paddingTop)-parseFloat(b.paddingBottom)-parseFloat(b.borderBottomWidth)])}else this.Vf(void 0)};k.ti=function(a){a=x(a).toString();delete this.De[a];this.render()};
function $l(a){var b=null;void 0!==a.keyboardEventTarget&&(b="string"===typeof a.keyboardEventTarget?document.getElementById(a.keyboardEventTarget):a.keyboardEventTarget);var c={},d={};if(void 0===a.logo||"boolean"===typeof a.logo&&a.logo)d["data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAMAAABEpIrGAAAAA3NCSVQICAjb4U/gAAAACXBIWXMAAAHGAAABxgEXwfpGAAAAGXRFWHRTb2Z0d2FyZQB3d3cuaW5rc2NhcGUub3Jnm+48GgAAAhNQTFRF////AP//AICAgP//AFVVQECA////K1VVSbbbYL/fJ05idsTYJFtbbcjbJllmZszWWMTOIFhoHlNiZszTa9DdUcHNHlNlV8XRIVdiasrUHlZjIVZjaMnVH1RlIFRkH1RkH1ZlasvYasvXVsPQH1VkacnVa8vWIVZjIFRjVMPQa8rXIVVkXsXRsNveIFVkIFZlIVVj3eDeh6GmbMvXH1ZkIFRka8rWbMvXIFVkIFVjIFVkbMvWH1VjbMvWIFVlbcvWIFVla8vVIFVkbMvWbMvVH1VkbMvWIFVlbcvWIFVkbcvVbMvWjNPbIFVkU8LPwMzNIFVkbczWIFVkbsvWbMvXIFVkRnB8bcvW2+TkW8XRIFVkIlZlJVloJlpoKlxrLl9tMmJwOWd0Omh1RXF8TneCT3iDUHiDU8LPVMLPVcLPVcPQVsPPVsPQV8PQWMTQWsTQW8TQXMXSXsXRX4SNX8bSYMfTYcfTYsfTY8jUZcfSZsnUaIqTacrVasrVa8jTa8rWbI2VbMvWbcvWdJObdcvUdszUd8vVeJaee87Yfc3WgJyjhqGnitDYjaarldPZnrK2oNbborW5o9bbo9fbpLa6q9ndrL3ArtndscDDutzfu8fJwN7gwt7gxc/QyuHhy+HizeHi0NfX0+Pj19zb1+Tj2uXk29/e3uLg3+Lh3+bl4uXj4ufl4+fl5Ofl5ufl5ujm5+jmySDnBAAAAFp0Uk5TAAECAgMEBAYHCA0NDg4UGRogIiMmKSssLzU7PkJJT1JTVFliY2hrdHZ3foSFhYeJjY2QkpugqbG1tre5w8zQ09XY3uXn6+zx8vT09vf4+Pj5+fr6/P39/f3+gz7SsAAAAVVJREFUOMtjYKA7EBDnwCPLrObS1BRiLoJLnte6CQy8FLHLCzs2QUG4FjZ5GbcmBDDjxJBXDWxCBrb8aM4zbkIDzpLYnAcE9VXlJSWlZRU13koIeW57mGx5XjoMZEUqwxWYQaQbSzLSkYGfKFSe0QMsX5WbjgY0YS4MBplemI4BdGBW+DQ11eZiymfqQuXZIjqwyadPNoSZ4L+0FVM6e+oGI6g8a9iKNT3o8kVzNkzRg5lgl7p4wyRUL9Yt2jAxVh6mQCogae6GmflI8p0r13VFWTHBQ0rWPW7ahgWVcPm+9cuLoyy4kCJDzCm6d8PSFoh0zvQNC5OjDJhQopPPJqph1doJBUD5tnkbZiUEqaCnB3bTqLTFG1bPn71kw4b+GFdpLElKIzRxxgYgWNYc5SCENVHKeUaltHdXx0dZ8uBI1hJ2UUDgq82CM2MwKeibqAvSO7MCABq0wXEPiqWEAAAAAElFTkSuQmCC"]=
"http://openlayers.org/";else{var e=a.logo;"string"===typeof e?d[e]="":e instanceof HTMLElement?d[x(e).toString()]=e:e&&(la("string"==typeof e.href,44),la("string"==typeof e.src,45),d[e.src]=e.href)}e=a.layers instanceof zi?a.layers:new zi({layers:a.layers});c.layergroup=e;c.target=a.target;c.view=void 0!==a.view?a.view:new Bd;var e=lh,f;void 0!==a.renderer?Array.isArray(a.renderer)?f=a.renderer:"string"===typeof a.renderer?f=[a.renderer]:la(!1,46):f=Zl;var g,h;g=0;for(h=f.length;g<h;++g){var l=f[g];
if("canvas"==l){if(Mf){e=Lk;break}}else if("dom"==l){e=Tk;break}else if("webgl"==l&&Ff){e=Wl;break}}void 0!==a.controls?Array.isArray(a.controls)?f=new re(a.controls.slice()):(la(a.controls instanceof re,47),f=a.controls):f=wf();void 0!==a.interactions?Array.isArray(a.interactions)?g=new re(a.interactions.slice()):(la(a.interactions instanceof re,48),g=a.interactions):g=yi();void 0!==a.overlays?Array.isArray(a.overlays)?a=new re(a.overlays.slice()):(la(a.overlays instanceof re,49),a=a.overlays):a=
new re;return{controls:f,interactions:g,keyboardEventTarget:b,logos:d,overlays:a,To:e,values:c}}Ii();function am(a){Xa.call(this);this.o=a.id;this.j=void 0!==a.insertFirst?a.insertFirst:!0;this.s=void 0!==a.stopEvent?a.stopEvent:!0;this.f=document.createElement("DIV");this.f.className="ol-overlay-container";this.f.style.position="absolute";this.autoPan=void 0!==a.autoPan?a.autoPan:!1;this.i=void 0!==a.autoPanAnimation?a.autoPanAnimation:{};this.l=void 0!==a.autoPanMargin?a.autoPanMargin:20;this.a={Ld:"",de:"",Ce:"",Ee:"",visible:!0};this.c=null;B(this,Za("element"),this.Gk,this);B(this,Za("map"),
this.Qk,this);B(this,Za("offset"),this.Uk,this);B(this,Za("position"),this.Wk,this);B(this,Za("positioning"),this.Xk,this);void 0!==a.element&&this.ci(a.element);this.ii(void 0!==a.offset?a.offset:[0,0]);this.li(void 0!==a.positioning?a.positioning:"top-left");void 0!==a.position&&this.tf(a.position)}w(am,Xa);k=am.prototype;k.Sd=function(){return this.get("element")};k.Oa=function(){return this.o};k.fe=function(){return this.get("map")};k.Ig=function(){return this.get("offset")};k.dh=function(){return this.get("position")};
k.Jg=function(){return this.get("positioning")};k.Gk=function(){gf(this.f);var a=this.Sd();a&&this.f.appendChild(a)};k.Qk=function(){this.c&&(ff(this.f),Ea(this.c),this.c=null);var a=this.fe();a&&(this.c=B(a,"postrender",this.render,this),bm(this),a=this.s?a.v:a.A,this.j?a.insertBefore(this.f,a.childNodes[0]||null):a.appendChild(this.f))};k.render=function(){bm(this)};k.Uk=function(){bm(this)};
k.Wk=function(){bm(this);if(void 0!==this.get("position")&&this.autoPan){var a=this.fe();if(void 0!==a&&a.xc()){var b=cm(a.xc(),a.kb()),c=this.Sd(),d=c.offsetWidth,e=c.currentStyle||ja.getComputedStyle(c),d=d+(parseInt(e.marginLeft,10)+parseInt(e.marginRight,10)),e=c.offsetHeight,f=c.currentStyle||ja.getComputedStyle(c),e=e+(parseInt(f.marginTop,10)+parseInt(f.marginBottom,10)),g=cm(c,[d,e]),c=this.l;Nb(b,g)||(d=g[0]-b[0],e=b[2]-g[2],f=g[1]-b[1],g=b[3]-g[3],b=[0,0],0>d?b[0]=d-c:0>e&&(b[0]=Math.abs(e)+
c),0>f?b[1]=f-c:0>g&&(b[1]=Math.abs(g)+c),0===b[0]&&0===b[1])||(c=a.aa().bb(),d=a.Ga(c),b=[d[0]+b[0],d[1]+b[1]],this.i&&(this.i.source=c,a.$a(Sd(this.i))),a.aa().ob(a.Na(b)))}}};k.Xk=function(){bm(this)};k.ci=function(a){this.set("element",a)};k.setMap=function(a){this.set("map",a)};k.ii=function(a){this.set("offset",a)};k.tf=function(a){this.set("position",a)};function cm(a,b){var c=a.getBoundingClientRect(),d=c.left+ja.pageXOffset,c=c.top+ja.pageYOffset;return[d,c,d+b[0],c+b[1]]}
k.li=function(a){this.set("positioning",a)};function dm(a,b){a.a.visible!==b&&(a.f.style.display=b?"":"none",a.a.visible=b)}
function bm(a){var b=a.fe(),c=a.dh();if(void 0!==b&&b.f&&void 0!==c){var c=b.Ga(c),d=b.kb(),b=a.f.style,e=a.Ig(),f=a.Jg(),g=e[0],e=e[1];if("bottom-right"==f||"center-right"==f||"top-right"==f)""!==a.a.de&&(a.a.de=b.left=""),g=Math.round(d[0]-c[0]-g)+"px",a.a.Ce!=g&&(a.a.Ce=b.right=g);else{""!==a.a.Ce&&(a.a.Ce=b.right="");if("bottom-center"==f||"center-center"==f||"top-center"==f)g-=a.f.offsetWidth/2;g=Math.round(c[0]+g)+"px";a.a.de!=g&&(a.a.de=b.left=g)}if("bottom-left"==f||"bottom-center"==f||"bottom-right"==
f)""!==a.a.Ee&&(a.a.Ee=b.top=""),c=Math.round(d[1]-c[1]-e)+"px",a.a.Ld!=c&&(a.a.Ld=b.bottom=c);else{""!==a.a.Ld&&(a.a.Ld=b.bottom="");if("center-left"==f||"center-center"==f||"center-right"==f)e-=a.f.offsetHeight/2;c=Math.round(c[1]+e)+"px";a.a.Ee!=c&&(a.a.Ee=b.top=c)}dm(a,!0)}else dm(a,!1)};function em(a){a=a?a:{};this.l=void 0!==a.collapsed?a.collapsed:!0;this.j=void 0!==a.collapsible?a.collapsible:!0;this.j||(this.l=!1);var b=void 0!==a.className?a.className:"ol-overviewmap",c=void 0!==a.tipLabel?a.tipLabel:"Overview map",d=void 0!==a.collapseLabel?a.collapseLabel:"\u00ab";"string"===typeof d?(this.o=document.createElement("span"),this.o.textContent=d):this.o=d;d=void 0!==a.label?a.label:"\u00bb";"string"===typeof d?(this.v=document.createElement("span"),this.v.textContent=d):this.v=
d;var e=this.j&&!this.l?this.o:this.v,d=document.createElement("button");d.setAttribute("type","button");d.title=c;d.appendChild(e);B(d,"click",this.bm,this);c=document.createElement("DIV");c.className="ol-overviewmap-map";var f=this.f=new I({controls:new re,interactions:new re,target:c,view:a.view});a.layers&&a.layers.forEach(function(a){f.jg(a)},this);e=document.createElement("DIV");e.className="ol-overviewmap-box";e.style.boxSizing="border-box";this.A=new am({position:[0,0],positioning:"bottom-left",
element:e});this.f.kg(this.A);e=document.createElement("div");e.className=b+" ol-unselectable ol-control"+(this.l&&this.j?" ol-collapsed":"")+(this.j?"":" ol-uncollapsible");e.appendChild(c);e.appendChild(d);jf.call(this,{element:e,render:a.render?a.render:fm,target:a.target})}w(em,jf);k=em.prototype;
k.setMap=function(a){var b=this.a;a!==b&&(b&&(b=b.aa())&&Ka(b,Za(Ed),this.be,this),jf.prototype.setMap.call(this,a),a&&(this.s.push(B(a,"propertychange",this.Rk,this)),0===this.f.ah().gc()&&this.f.gi(a.wc()),a=a.aa()))&&(B(a,Za(Ed),this.be,this),Jd(a)&&(this.f.Wc(),gm(this)))};k.Rk=function(a){"view"===a.key&&((a=a.oldValue)&&Ka(a,Za(Ed),this.be,this),a=this.a.aa(),B(a,Za(Ed),this.be,this))};k.be=function(){this.f.aa().ge(this.a.aa().Ma())};
function fm(){var a=this.a,b=this.f;if(a.f&&b.f){var c=a.kb(),a=a.aa().Ic(c),d=b.kb(),c=b.aa().Ic(d),e=b.Ga(bc(a)),f=b.Ga($b(a)),b=Math.abs(e[0]-f[0]),e=Math.abs(e[1]-f[1]),f=d[0],d=d[1];b<.1*f||e<.1*d||b>.75*f||e>.75*d?gm(this):Nb(c,a)||(a=this.f,c=this.a.aa(),a.aa().ob(c.bb()))}hm(this)}function gm(a){var b=a.a;a=a.f;var c=b.kb(),b=b.aa().Ic(c),c=a.kb();a=a.aa();lc(b,1/(.1*Math.pow(2,Math.log(7.5)/Math.LN2/2)));a.af(b,c)}
function hm(a){var b=a.a,c=a.f;if(b.f&&c.f){var d=b.kb(),e=b.aa(),f=c.aa(),c=e.Ma(),b=a.A,g=a.A.Sd(),h=e.Ic(d),d=f.$(),e=Zb(h),f=ac(h),l;if(a=a.a.aa().bb())l=[e[0]-a[0],e[1]-a[1]],zb(l,c),ub(l,a);b.tf(l);g&&(g.style.width=Math.abs((e[0]-f[0])/d)+"px",g.style.height=Math.abs((f[1]-e[1])/d)+"px")}}k.bm=function(a){a.preventDefault();im(this)};
function im(a){a.element.classList.toggle("ol-collapsed");a.l?ef(a.o,a.v):ef(a.v,a.o);a.l=!a.l;var b=a.f;a.l||b.f||(b.Wc(),gm(a),Ja(b,"postrender",function(){hm(this)},a))}k.am=function(){return this.j};k.dm=function(a){this.j!==a&&(this.j=a,this.element.classList.toggle("ol-uncollapsible"),!a&&this.l&&im(this))};k.cm=function(a){this.j&&this.l!==a&&im(this)};k.$l=function(){return this.l};k.lk=function(){return this.f};function jm(a){a=a?a:{};var b=void 0!==a.className?a.className:"ol-scale-line";this.j=document.createElement("DIV");this.j.className=b+"-inner";this.f=document.createElement("DIV");this.f.className=b+" ol-unselectable";this.f.appendChild(this.j);this.v=null;this.o=void 0!==a.minWidth?a.minWidth:64;this.l=!1;this.H=void 0;this.A="";jf.call(this,{element:this.f,render:a.render?a.render:km,target:a.target});B(this,Za(lm),this.O,this);this.D(a.units||mm)}w(jm,jf);var nm=[1,2,5];jm.prototype.Ab=function(){return this.get(lm)};
function km(a){(a=a.frameState)?this.v=a.viewState:this.v=null;om(this)}jm.prototype.O=function(){om(this)};jm.prototype.D=function(a){this.set(lm,a)};
function om(a){var b=a.v;if(b){var c=b.projection,d=c.cc(),b=c.getPointResolution(b.resolution,b.center)*d,d=a.o*b,c="",e=a.Ab();e==pm?(c=rc.degrees,b/=c,d<c/60?(c="\u2033",b*=3600):d<c?(c="\u2032",b*=60):c="\u00b0"):e==qm?.9144>d?(c="in",b/=.0254):1609.344>d?(c="ft",b/=.3048):(c="mi",b/=1609.344):e==rm?(b/=1852,c="nm"):e==mm?1>d?(c="mm",b*=1E3):1E3>d?c="m":(c="km",b/=1E3):e==sm?.9144>d?(c="in",b*=39.37):1609.344>d?(c="ft",b/=.30480061):(c="mi",b/=1609.3472):la(!1,33);for(var e=3*Math.floor(Math.log(a.o*
b)/Math.log(10)),f;;){f=nm[(e%3+3)%3]*Math.pow(10,Math.floor(e/3));d=Math.round(f/b);if(isNaN(d)){a.f.style.display="none";a.l=!1;return}if(d>=a.o)break;++e}b=f+" "+c;a.A!=b&&(a.j.innerHTML=b,a.A=b);a.H!=d&&(a.j.style.width=d+"px",a.H=d);a.l||(a.f.style.display="",a.l=!0)}else a.l&&(a.f.style.display="none",a.l=!1)}var lm="units",pm="degrees",qm="imperial",rm="nautical",mm="metric",sm="us";function tm(a){a=a?a:{};this.f=void 0;this.l=um;this.v=[];this.H=this.o=0;this.T=null;this.ha=!1;this.W=void 0!==a.duration?a.duration:200;var b=void 0!==a.className?a.className:"ol-zoomslider",c=document.createElement("button");c.setAttribute("type","button");c.className=b+"-thumb ol-unselectable";var d=document.createElement("div");d.className=b+" ol-unselectable ol-control";d.appendChild(c);this.j=new wg(d);B(this.j,cg,this.Fk,this);B(this.j,dg,this.Lg,this);B(this.j,eg,this.Mg,this);B(d,"click",
this.Ek,this);B(c,"click",Qa);jf.call(this,{element:d,render:a.render?a.render:vm})}w(tm,jf);tm.prototype.ma=function(){Oa(this.j);jf.prototype.ma.call(this)};var um=0;k=tm.prototype;k.setMap=function(a){jf.prototype.setMap.call(this,a);a&&a.render()};
function vm(a){if(a.frameState){if(!this.ha){var b=this.element,c=b.offsetWidth,d=b.offsetHeight,e=b.firstElementChild,f=ja.getComputedStyle(e),b=e.offsetWidth+parseFloat(f.marginRight)+parseFloat(f.marginLeft),e=e.offsetHeight+parseFloat(f.marginTop)+parseFloat(f.marginBottom);this.T=[b,e];c>d?(this.l=1,this.H=c-b):(this.l=um,this.o=d-e);this.ha=!0}a=a.frameState.viewState.resolution;a!==this.f&&(this.f=a,wm(this,a))}}
k.Ek=function(a){var b=this.a,c=b.aa(),d=c.$();b.$a(Ud({resolution:d,duration:this.W,easing:Md}));a=xm(this,ma(1===this.l?(a.offsetX-this.T[0]/2)/this.H:(a.offsetY-this.T[1]/2)/this.o,0,1));c.Zb(c.constrainResolution(a))};
k.Fk=function(a){if(!this.A&&a.b.target===this.element.firstElementChild&&(Kd(this.a.aa(),1),this.D=a.clientX,this.O=a.clientY,this.A=!0,0===this.v.length)){a=this.Lg;var b=this.Mg;this.v.push(B(document,"mousemove",a,this),B(document,"touchmove",a,this),B(document,dg,a,this),B(document,"mouseup",b,this),B(document,"touchend",b,this),B(document,eg,b,this))}};
k.Lg=function(a){if(this.A){var b=this.element.firstElementChild;this.f=xm(this,ma(1===this.l?(a.clientX-this.D+parseInt(b.style.left,10))/this.H:(a.clientY-this.O+parseInt(b.style.top,10))/this.o,0,1));this.a.aa().Zb(this.f);wm(this,this.f);this.D=a.clientX;this.O=a.clientY}};k.Mg=function(){if(this.A){var a=this.a,b=a.aa();Kd(b,-1);a.$a(Ud({resolution:this.f,duration:this.W,easing:Md}));a=b.constrainResolution(this.f);b.Zb(a);this.A=!1;this.O=this.D=void 0;this.v.forEach(Ea);this.v.length=0}};
function wm(a,b){var c;c=1-Id(a.a.aa())(b);var d=a.element.firstElementChild;1==a.l?d.style.left=a.H*c+"px":d.style.top=a.o*c+"px"}function xm(a,b){return Hd(a.a.aa())(1-b)};function ym(a){a=a?a:{};this.f=a.extent?a.extent:null;var b=void 0!==a.className?a.className:"ol-zoom-extent",c=void 0!==a.label?a.label:"E",d=void 0!==a.tipLabel?a.tipLabel:"Fit to extent",e=document.createElement("button");e.setAttribute("type","button");e.title=d;e.appendChild("string"===typeof c?document.createTextNode(c):c);B(e,"click",this.l,this);c=document.createElement("div");c.className=b+" ol-unselectable ol-control";c.appendChild(e);jf.call(this,{element:c,target:a.target})}w(ym,jf);
ym.prototype.l=function(a){a.preventDefault();var b=this.a;a=b.aa();var c=this.f?this.f:a.j.C(),b=b.kb();a.af(c,b)};function zm(a){Xa.call(this);a=a?a:{};this.a=null;B(this,Za("tracking"),this.Dl,this);this.qf(void 0!==a.tracking?a.tracking:!1)}w(zm,Xa);k=zm.prototype;k.ma=function(){this.qf(!1);Xa.prototype.ma.call(this)};
k.$n=function(a){if(null!==a.alpha){var b=sa(a.alpha);this.set("alpha",b);"boolean"===typeof a.absolute&&a.absolute?this.set("heading",b):"number"===typeof a.webkitCompassHeading&&-1!=a.webkitCompassAccuracy&&this.set("heading",sa(a.webkitCompassHeading))}null!==a.beta&&this.set("beta",sa(a.beta));null!==a.gamma&&this.set("gamma",sa(a.gamma));this.u()};k.Lj=function(){return this.get("alpha")};k.Oj=function(){return this.get("beta")};k.Vj=function(){return this.get("gamma")};k.Cl=function(){return this.get("heading")};
k.Xg=function(){return this.get("tracking")};k.Dl=function(){if(Nf){var a=this.Xg();a&&!this.a?this.a=B(ja,"deviceorientation",this.$n,this):a||null===this.a||(Ea(this.a),this.a=null)}};k.qf=function(a){this.set("tracking",a)};function Am(a){Xa.call(this);this.i=void 0;this.a="geometry";this.c=null;this.l=void 0;this.f=null;B(this,Za(this.a),this.$d,this);void 0!==a&&(a instanceof Qc||!a?this.Xa(a):this.G(a))}w(Am,Xa);k=Am.prototype;k.clone=function(){var a=new Am(this.N());a.Cc(this.a);var b=this.Y();b&&a.Xa(b.clone());(b=this.c)&&a.rf(b);return a};k.Y=function(){return this.get(this.a)};k.Oa=function(){return this.i};k.Xj=function(){return this.a};k.El=function(){return this.c};k.hc=function(){return this.l};k.Fl=function(){this.u()};
k.$d=function(){this.f&&(Ea(this.f),this.f=null);var a=this.Y();a&&(this.f=B(a,"change",this.Fl,this));this.u()};k.Xa=function(a){this.set(this.a,a)};k.rf=function(a){this.l=(this.c=a)?Bm(a):void 0;this.u()};k.Xb=function(a){this.i=a;this.u()};k.Cc=function(a){Ka(this,Za(this.a),this.$d,this);this.a=a;B(this,Za(this.a),this.$d,this);this.$d()};function Bm(a){if("function"!==typeof a){var b;Array.isArray(a)?b=a:(la(a instanceof Zi,41),b=[a]);a=function(){return b}}return a};var Cm=document.implementation.createDocument("","",null);function Dm(a,b){return Cm.createElementNS(a,b)}function Em(a,b){return Fm(a,b,[]).join("")}function Fm(a,b,c){if(a.nodeType==Node.CDATA_SECTION_NODE||a.nodeType==Node.TEXT_NODE)b?c.push(String(a.nodeValue).replace(/(\r\n|\r|\n)/g,"")):c.push(a.nodeValue);else for(a=a.firstChild;a;a=a.nextSibling)Fm(a,b,c);return c}function Gm(a){return a instanceof Document}function Hm(a){return a instanceof Node}
function Im(a){return(new DOMParser).parseFromString(a,"application/xml")}function Jm(a,b){return function(c,d){var e=a.call(b,c,d);void 0!==e&&eb(d[d.length-1],e)}}function Km(a,b){return function(c,d){var e=a.call(void 0!==b?b:this,c,d);void 0!==e&&d[d.length-1].push(e)}}function Lm(a,b){return function(c,d){var e=a.call(void 0!==b?b:this,c,d);void 0!==e&&(d[d.length-1]=e)}}
function Mm(a){return function(b,c){var d=a.call(this,b,c);if(void 0!==d){var e=c[c.length-1],f=b.localName,g;f in e?g=e[f]:g=e[f]=[];g.push(d)}}}function K(a,b){return function(c,d){var e=a.call(this,c,d);void 0!==e&&(d[d.length-1][void 0!==b?b:c.localName]=e)}}function L(a,b){return function(c,d,e){a.call(void 0!==b?b:this,c,d,e);e[e.length-1].node.appendChild(c)}}
function Nm(a){var b,c;return function(d,e,f){if(!b){b={};var g={};g[d.localName]=a;b[d.namespaceURI]=g;c=Om(d.localName)}Pm(b,c,e,f)}}function Om(a,b){return function(c,d,e){c=d[d.length-1].node;d=a;void 0===d&&(d=e);e=b;void 0===b&&(e=c.namespaceURI);return Dm(e,d)}}var Qm=Om();function Rm(a,b){for(var c=b.length,d=Array(c),e=0;e<c;++e)d[e]=a[b[e]];return d}function N(a,b,c){c=void 0!==c?c:{};var d,e;d=0;for(e=a.length;d<e;++d)c[a[d]]=b;return c}
function Sm(a,b,c,d){for(b=b.firstElementChild;b;b=b.nextElementSibling){var e=a[b.namespaceURI];void 0!==e&&(e=e[b.localName])&&e.call(d,b,c)}}function O(a,b,c,d,e){d.push(a);Sm(b,c,d,e);return d.pop()}function Pm(a,b,c,d,e,f){for(var g=(void 0!==e?e:c).length,h,l,m=0;m<g;++m)h=c[m],void 0!==h&&(l=b.call(f,h,d,void 0!==e?e[m]:void 0),void 0!==l&&a[l.namespaceURI][l.localName].call(f,l,h,d))}function Tm(a,b,c,d,e,f,g){e.push(a);Pm(b,c,d,e,f,g);e.pop()};function Um(a,b,c,d){return function(e,f,g){var h=new XMLHttpRequest;h.open("GET","function"===typeof a?a(e,f,g):a,!0);"arraybuffer"==b.X()&&(h.responseType="arraybuffer");h.onload=function(){if(!h.status||200<=h.status&&300>h.status){var a=b.X(),e;"json"==a||"text"==a?e=h.responseText:"xml"==a?(e=h.responseXML)||(e=Im(h.responseText)):"arraybuffer"==a&&(e=h.response);e?c.call(this,b.Fa(e,{featureProjection:g}),b.Ra(e)):d.call(this)}else d.call(this)}.bind(this);h.send()}}
function Vm(a,b){return Um(a,b,function(a,b){this.uf(b);this.di(a)},function(){this.state=3;rh(this)})}function Wm(a,b){return Um(a,b,function(a){this.Hc(a)},ha)};function Xm(){this.defaultDataProjection=null}function Ym(a,b,c){var d;c&&(d={dataProjection:c.dataProjection?c.dataProjection:a.Ra(b),featureProjection:c.featureProjection});return Zm(a,d)}function Zm(a,b){var c;b&&(c={featureProjection:b.featureProjection,dataProjection:b.dataProjection?b.dataProjection:a.defaultDataProjection,rightHanded:b.rightHanded},b.decimals&&(c.decimals=b.decimals));return c}
function $m(a,b,c){var d=c?vc(c.featureProjection):null,e=c?vc(c.dataProjection):null,f;d&&e&&!Lc(d,e)?a instanceof Qc?f=(b?a.clone():a).lb(b?d:e,b?e:d):f=Pc(b?a.slice():a,b?d:e,b?e:d):f=a;if(b&&c&&c.decimals){var g=Math.pow(10,c.decimals);a=function(a){for(var b=0,c=a.length;b<c;++b)a[b]=Math.round(a[b]*g)/g;return a};Array.isArray(f)?a(f):f.rc(a)}return f};function an(){this.defaultDataProjection=null}w(an,Xm);function bn(a){return"string"===typeof a?(a=JSON.parse(a))?a:null:null!==a?a:null}k=an.prototype;k.X=function(){return"json"};k.Vb=function(a,b){return this.Tc(bn(a),Ym(this,a,b))};k.Fa=function(a,b){return this.If(bn(a),Ym(this,a,b))};k.Uc=function(a,b){return this.Oh(bn(a),Ym(this,a,b))};k.Ra=function(a){return this.Vh(bn(a))};k.Cd=function(a,b){return JSON.stringify(this.Xc(a,b))};k.ac=function(a,b){return JSON.stringify(this.He(a,b))};
k.Yc=function(a,b){return JSON.stringify(this.Ie(a,b))};function cn(a,b,c,d,e,f){var g=NaN,h=NaN,l=(c-b)/d;if(0!==l)if(1==l)g=a[b],h=a[b+1];else if(2==l)g=(1-e)*a[b]+e*a[b+d],h=(1-e)*a[b+1]+e*a[b+d+1];else{var h=a[b],l=a[b+1],m=0,g=[0],n;for(n=b+d;n<c;n+=d){var p=a[n],q=a[n+1],m=m+Math.sqrt((p-h)*(p-h)+(q-l)*(q-l));g.push(m);h=p;l=q}c=e*m;l=0;m=g.length;for(n=!1;l<m;)e=l+(m-l>>1),h=+ab(g[e],c),0>h?l=e+1:(m=e,n=!h);e=n?l:~l;0>e?(c=(c-g[-e-2])/(g[-e-1]-g[-e-2]),b+=(-e-2)*d,g=va(a[b],a[b+d],c),h=va(a[b+1],a[b+d+1],c)):(g=a[b+e*d],h=a[b+e*d+1])}return f?(f[0]=
g,f[1]=h,f):[g,h]}function dn(a,b,c,d,e,f){if(c==b)return null;if(e<a[b+d-1])return f?(c=a.slice(b,b+d),c[d-1]=e,c):null;if(a[c-1]<e)return f?(c=a.slice(c-d,c),c[d-1]=e,c):null;if(e==a[b+d-1])return a.slice(b,b+d);b/=d;for(c/=d;b<c;)f=b+c>>1,e<a[(f+1)*d-1]?c=f:b=f+1;c=a[b*d-1];if(e==c)return a.slice((b-1)*d,(b-1)*d+d);f=(e-c)/(a[(b+1)*d-1]-c);c=[];var g;for(g=0;g<d-1;++g)c.push(va(a[(b-1)*d+g],a[b*d+g],f));c.push(e);return c}
function en(a,b,c,d,e,f){var g=0;if(f)return dn(a,g,b[b.length-1],c,d,e);if(d<a[c-1])return e?(a=a.slice(0,c),a[c-1]=d,a):null;if(a[a.length-1]<d)return e?(a=a.slice(a.length-c),a[c-1]=d,a):null;e=0;for(f=b.length;e<f;++e){var h=b[e];if(g!=h){if(d<a[g+c-1])break;if(d<=a[h-1])return dn(a,g,h,c,d,!1);g=h}}return null};function P(a,b){Sc.call(this);this.c=null;this.A=this.H=this.j=-1;this.sa(a,b)}w(P,Sc);k=P.prototype;k.sj=function(a){this.B?eb(this.B,a):this.B=a.slice();this.u()};k.clone=function(){var a=new P(null);a.ba(this.ka,this.B.slice());return a};k.vb=function(a,b,c,d){if(d<Kb(this.C(),a,b))return d;this.A!=this.g&&(this.H=Math.sqrt(Zc(this.B,0,this.B.length,this.a,0)),this.A=this.g);return ad(this.B,0,this.B.length,this.a,this.H,!1,a,b,c,d)};
k.Ij=function(a,b){return pd(this.B,0,this.B.length,this.a,a,b)};k.gm=function(a,b){return"XYM"!=this.ka&&"XYZM"!=this.ka?null:dn(this.B,0,this.B.length,this.a,a,void 0!==b?b:!1)};k.Z=function(){return fd(this.B,0,this.B.length,this.a)};k.zg=function(a,b){return cn(this.B,0,this.B.length,this.a,a,b)};k.hm=function(){var a=this.B,b=this.a,c=a[0],d=a[1],e=0,f;for(f=0+b;f<this.B.length;f+=b)var g=a[f],h=a[f+1],e=e+Math.sqrt((g-c)*(g-c)+(h-d)*(h-d)),c=g,d=h;return e};
function nj(a){a.j!=a.g&&(a.c=a.zg(.5,a.c),a.j=a.g);return a.c}k.Lc=function(a){var b=[];b.length=hd(this.B,0,this.B.length,this.a,a,b,0);a=new P(null);a.ba("XY",b);return a};k.X=function(){return"LineString"};k.La=function(a){return qd(this.B,0,this.B.length,this.a,a)};k.sa=function(a,b){a?(Vc(this,b,a,1),this.B||(this.B=[]),this.B.length=dd(this.B,0,a,this.a),this.u()):this.ba("XY",null)};k.ba=function(a,b){Uc(this,a,b);this.u()};function Q(a,b){Sc.call(this);this.c=[];this.j=this.A=-1;this.sa(a,b)}w(Q,Sc);k=Q.prototype;k.tj=function(a){this.B?eb(this.B,a.la().slice()):this.B=a.la().slice();this.c.push(this.B.length);this.u()};k.clone=function(){var a=new Q(null);a.ba(this.ka,this.B.slice(),this.c.slice());return a};k.vb=function(a,b,c,d){if(d<Kb(this.C(),a,b))return d;this.j!=this.g&&(this.A=Math.sqrt($c(this.B,0,this.c,this.a,0)),this.j=this.g);return bd(this.B,0,this.c,this.a,this.A,!1,a,b,c,d)};
k.jm=function(a,b,c){return"XYM"!=this.ka&&"XYZM"!=this.ka||0===this.B.length?null:en(this.B,this.c,this.a,a,void 0!==b?b:!1,void 0!==c?c:!1)};k.Z=function(){return gd(this.B,0,this.c,this.a)};k.Hb=function(){return this.c};k.ck=function(a){if(0>a||this.c.length<=a)return null;var b=new P(null);b.ba(this.ka,this.B.slice(0===a?0:this.c[a-1],this.c[a]));return b};
k.ld=function(){var a=this.B,b=this.c,c=this.ka,d=[],e=0,f,g;f=0;for(g=b.length;f<g;++f){var h=b[f],l=new P(null);l.ba(c,a.slice(e,h));d.push(l);e=h}return d};function oj(a){var b=[],c=a.B,d=0,e=a.c;a=a.a;var f,g;f=0;for(g=e.length;f<g;++f){var h=e[f],d=cn(c,d,h,a,.5);eb(b,d);d=h}return b}k.Lc=function(a){var b=[],c=[],d=this.B,e=this.c,f=this.a,g=0,h=0,l,m;l=0;for(m=e.length;l<m;++l){var n=e[l],h=hd(d,g,n,f,a,b,h);c.push(h);g=n}b.length=h;a=new Q(null);a.ba("XY",b,c);return a};k.X=function(){return"MultiLineString"};
k.La=function(a){a:{var b=this.B,c=this.c,d=this.a,e=0,f,g;f=0;for(g=c.length;f<g;++f){if(qd(b,e,c[f],d,a)){a=!0;break a}e=c[f]}a=!1}return a};k.sa=function(a,b){if(a){Vc(this,b,a,2);this.B||(this.B=[]);var c=ed(this.B,0,a,this.a,this.c);this.B.length=0===c.length?0:c[c.length-1];this.u()}else this.ba("XY",null,this.c)};k.ba=function(a,b,c){Uc(this,a,b);this.c=c;this.u()};
function fn(a,b){var c=a.ka,d=[],e=[],f,g;f=0;for(g=b.length;f<g;++f){var h=b[f];0===f&&(c=h.ka);eb(d,h.la());e.push(d.length)}a.ba(c,d,e)};function R(a,b){Sc.call(this);this.sa(a,b)}w(R,Sc);k=R.prototype;k.vj=function(a){this.B?eb(this.B,a.la()):this.B=a.la().slice();this.u()};k.clone=function(){var a=new R(null);a.ba(this.ka,this.B.slice());return a};k.vb=function(a,b,c,d){if(d<Kb(this.C(),a,b))return d;var e=this.B,f=this.a,g,h,l;g=0;for(h=e.length;g<h;g+=f)if(l=ra(a,b,e[g],e[g+1]),l<d){d=l;for(l=0;l<f;++l)c[l]=e[g+l];c.length=f}return d};k.Z=function(){return fd(this.B,0,this.B.length,this.a)};
k.nk=function(a){var b=this.B?this.B.length/this.a:0;if(0>a||b<=a)return null;b=new C(null);b.ba(this.ka,this.B.slice(a*this.a,(a+1)*this.a));return b};k.he=function(){var a=this.B,b=this.ka,c=this.a,d=[],e,f;e=0;for(f=a.length;e<f;e+=c){var g=new C(null);g.ba(b,a.slice(e,e+c));d.push(g)}return d};k.X=function(){return"MultiPoint"};k.La=function(a){var b=this.B,c=this.a,d,e,f,g;d=0;for(e=b.length;d<e;d+=c)if(f=b[d],g=b[d+1],Mb(a,f,g))return!0;return!1};
k.sa=function(a,b){a?(Vc(this,b,a,1),this.B||(this.B=[]),this.B.length=dd(this.B,0,a,this.a),this.u()):this.ba("XY",null)};k.ba=function(a,b){Uc(this,a,b);this.u()};function S(a,b){Sc.call(this);this.c=[];this.A=-1;this.H=null;this.R=this.D=this.O=-1;this.j=null;this.sa(a,b)}w(S,Sc);k=S.prototype;k.wj=function(a){if(this.B){var b=this.B.length;eb(this.B,a.la());a=a.Hb().slice();var c,d;c=0;for(d=a.length;c<d;++c)a[c]+=b}else this.B=a.la().slice(),a=a.Hb().slice(),this.c.push();this.c.push(a);this.u()};k.clone=function(){for(var a=new S(null),b=this.c.length,c=Array(b),d=0;d<b;++d)c[d]=this.c[d].slice();gn(a,this.ka,this.B.slice(),c);return a};
k.vb=function(a,b,c,d){if(d<Kb(this.C(),a,b))return d;if(this.D!=this.g){var e=this.c,f=0,g=0,h,l;h=0;for(l=e.length;h<l;++h)var m=e[h],g=$c(this.B,f,m,this.a,g),f=m[m.length-1];this.O=Math.sqrt(g);this.D=this.g}e=pj(this);f=this.c;g=this.a;h=this.O;l=0;var m=[NaN,NaN],n,p;n=0;for(p=f.length;n<p;++n){var q=f[n];d=bd(e,l,q,g,h,!0,a,b,c,d,m);l=q[q.length-1]}return d};
k.zc=function(a,b){var c;a:{c=pj(this);var d=this.c,e=this.a,f=0;if(0!==d.length){var g,h;g=0;for(h=d.length;g<h;++g){var l=d[g];if(nd(c,f,l,e,a,b)){c=!0;break a}f=l[l.length-1]}}c=!1}return c};k.km=function(){var a=pj(this),b=this.c,c=0,d=0,e,f;e=0;for(f=b.length;e<f;++e)var g=b[e],d=d+Xc(a,c,g,this.a),c=g[g.length-1];return d};
k.Z=function(a){var b;void 0!==a?(b=pj(this).slice(),vd(b,this.c,this.a,a)):b=this.B;a=b;b=this.c;var c=this.a,d=0,e=[],f=0,g,h;g=0;for(h=b.length;g<h;++g){var l=b[g];e[f++]=gd(a,d,l,c,e[f]);d=l[l.length-1]}e.length=f;return e};
function qj(a){if(a.A!=a.g){var b=a.B,c=a.c,d=a.a,e=0,f=[],g,h;g=0;for(h=c.length;g<h;++g){var l=c[g],e=Sb(b,e,l[0],d);f.push((e[0]+e[2])/2,(e[1]+e[3])/2);e=l[l.length-1]}b=pj(a);c=a.c;d=a.a;g=0;h=[];l=0;for(e=c.length;l<e;++l){var m=c[l];h=od(b,g,m,d,f,2*l,h);g=m[m.length-1]}a.H=h;a.A=a.g}return a.H}k.$j=function(){var a=new R(null);a.ba("XY",qj(this).slice());return a};
function pj(a){if(a.R!=a.g){var b=a.B,c;a:{c=a.c;var d,e;d=0;for(e=c.length;d<e;++d)if(!td(b,c[d],a.a,void 0)){c=!1;break a}c=!0}c?a.j=b:(a.j=b.slice(),a.j.length=vd(a.j,a.c,a.a));a.R=a.g}return a.j}k.Lc=function(a){var b=[],c=[],d=this.B,e=this.c,f=this.a;a=Math.sqrt(a);var g=0,h=0,l,m;l=0;for(m=e.length;l<m;++l){var n=e[l],p=[],h=id(d,g,n,f,a,b,h,p);c.push(p);g=n[n.length-1]}b.length=h;d=new S(null);gn(d,"XY",b,c);return d};
k.qk=function(a){if(0>a||this.c.length<=a)return null;var b;0===a?b=0:(b=this.c[a-1],b=b[b.length-1]);a=this.c[a].slice();var c=a[a.length-1];if(0!==b){var d,e;d=0;for(e=a.length;d<e;++d)a[d]-=b}d=new D(null);d.ba(this.ka,this.B.slice(b,c),a);return d};k.Wd=function(){var a=this.ka,b=this.B,c=this.c,d=[],e=0,f,g,h,l;f=0;for(g=c.length;f<g;++f){var m=c[f].slice(),n=m[m.length-1];if(0!==e)for(h=0,l=m.length;h<l;++h)m[h]-=e;h=new D(null);h.ba(a,b.slice(e,n),m);d.push(h);e=n}return d};k.X=function(){return"MultiPolygon"};
k.La=function(a){a:{var b=pj(this),c=this.c,d=this.a,e=0,f,g;f=0;for(g=c.length;f<g;++f){var h=c[f];if(rd(b,e,h,d,a)){a=!0;break a}e=h[h.length-1]}a=!1}return a};k.sa=function(a,b){if(a){Vc(this,b,a,3);this.B||(this.B=[]);var c=this.B,d=this.a,e=this.c,f=0,e=e?e:[],g=0,h,l;h=0;for(l=a.length;h<l;++h)f=ed(c,f,a[h],d,e[g]),e[g++]=f,f=f[f.length-1];e.length=g;0===e.length?this.B.length=0:(c=e[e.length-1],this.B.length=0===c.length?0:c[c.length-1]);this.u()}else gn(this,"XY",null,this.c)};
function gn(a,b,c,d){Uc(a,b,c);a.c=d;a.u()}function hn(a,b){var c=a.ka,d=[],e=[],f,g,h;f=0;for(g=b.length;f<g;++f){var l=b[f];0===f&&(c=l.ka);var m=d.length;h=l.Hb();var n,p;n=0;for(p=h.length;n<p;++n)h[n]+=m;eb(d,l.la());e.push(h)}gn(a,c,d,e)};function jn(a){a=a?a:{};this.defaultDataProjection=null;this.b=a.geometryName}w(jn,an);
function kn(a,b){if(!a)return null;var c;if("number"===typeof a.x&&"number"===typeof a.y)c="Point";else if(a.points)c="MultiPoint";else if(a.paths)c=1===a.paths.length?"LineString":"MultiLineString";else if(a.rings){var d=a.rings,e=ln(a),f=[];c=[];var g,h;g=0;for(h=d.length;g<h;++g){var l=db(d[g]);sd(l,0,l.length,e.length)?f.push([d[g]]):c.push(d[g])}for(;c.length;){d=c.shift();e=!1;for(g=f.length-1;0<=g;g--)if(Nb((new jd(f[g][0])).C(),(new jd(d)).C())){f[g].push(d);e=!0;break}e||f.push([d.reverse()])}a=
za({},a);1===f.length?(c="Polygon",a.rings=f[0]):(c="MultiPolygon",a.rings=f)}return $m((0,mn[c])(a),!1,b)}function ln(a){var b="XY";!0===a.hasZ&&!0===a.hasM?b="XYZM":!0===a.hasZ?b="XYZ":!0===a.hasM&&(b="XYM");return b}function nn(a){a=a.ka;return{hasZ:"XYZ"===a||"XYZM"===a,hasM:"XYM"===a||"XYZM"===a}}
var mn={Point:function(a){return void 0!==a.m&&void 0!==a.z?new C([a.x,a.y,a.z,a.m],"XYZM"):void 0!==a.z?new C([a.x,a.y,a.z],"XYZ"):void 0!==a.m?new C([a.x,a.y,a.m],"XYM"):new C([a.x,a.y])},LineString:function(a){return new P(a.paths[0],ln(a))},Polygon:function(a){return new D(a.rings,ln(a))},MultiPoint:function(a){return new R(a.points,ln(a))},MultiLineString:function(a){return new Q(a.paths,ln(a))},MultiPolygon:function(a){return new S(a.rings,ln(a))}},on={Point:function(a){var b=a.Z(),c;a=a.ka;
"XYZ"===a?c={x:b[0],y:b[1],z:b[2]}:"XYM"===a?c={x:b[0],y:b[1],m:b[2]}:"XYZM"===a?c={x:b[0],y:b[1],z:b[2],m:b[3]}:"XY"===a?c={x:b[0],y:b[1]}:la(!1,34);return c},LineString:function(a){var b=nn(a);return{hasZ:b.hasZ,hasM:b.hasM,paths:[a.Z()]}},Polygon:function(a){var b=nn(a);return{hasZ:b.hasZ,hasM:b.hasM,rings:a.Z(!1)}},MultiPoint:function(a){var b=nn(a);return{hasZ:b.hasZ,hasM:b.hasM,points:a.Z()}},MultiLineString:function(a){var b=nn(a);return{hasZ:b.hasZ,hasM:b.hasM,paths:a.Z()}},MultiPolygon:function(a){var b=
nn(a);a=a.Z(!1);for(var c=[],d=0;d<a.length;d++)for(var e=a[d].length-1;0<=e;e--)c.push(a[d][e]);return{hasZ:b.hasZ,hasM:b.hasM,rings:c}}};k=jn.prototype;k.Tc=function(a,b){var c=kn(a.geometry,b),d=new Am;this.b&&d.Cc(this.b);d.Xa(c);b&&b.lf&&a.attributes[b.lf]&&d.Xb(a.attributes[b.lf]);a.attributes&&d.G(a.attributes);return d};
k.If=function(a,b){var c=b?b:{};if(a.features){var d=[],e=a.features,f,g;c.lf=a.objectIdFieldName;f=0;for(g=e.length;f<g;++f)d.push(this.Tc(e[f],c));return d}return[this.Tc(a,c)]};k.Oh=function(a,b){return kn(a,b)};k.Vh=function(a){return a.spatialReference&&a.spatialReference.wkid?vc("EPSG:"+a.spatialReference.wkid):null};function pn(a,b){return(0,on[a.X()])($m(a,!0,b),b)}k.Ie=function(a,b){return pn(a,Zm(this,b))};
k.Xc=function(a,b){b=Zm(this,b);var c={},d=a.Y();d&&(c.geometry=pn(d,b));d=a.N();delete d[a.a];c.attributes=Ca(d)?{}:d;b&&b.featureProjection&&(c.spatialReference={wkid:vc(b.featureProjection).eb.split(":").pop()});return c};k.He=function(a,b){b=Zm(this,b);var c=[],d,e;d=0;for(e=a.length;d<e;++d)c.push(this.Xc(a[d],b));return{features:c}};function qn(a){Qc.call(this);this.f=a?a:null;rn(this)}w(qn,Qc);function sn(a){var b=[],c,d;c=0;for(d=a.length;c<d;++c)b.push(a[c].clone());return b}function tn(a){var b,c;if(a.f)for(b=0,c=a.f.length;b<c;++b)Ka(a.f[b],"change",a.u,a)}function rn(a){var b,c;if(a.f)for(b=0,c=a.f.length;b<c;++b)B(a.f[b],"change",a.u,a)}k=qn.prototype;k.clone=function(){var a=new qn(null);a.ei(this.f);return a};
k.vb=function(a,b,c,d){if(d<Kb(this.C(),a,b))return d;var e=this.f,f,g;f=0;for(g=e.length;f<g;++f)d=e[f].vb(a,b,c,d);return d};k.zc=function(a,b){var c=this.f,d,e;d=0;for(e=c.length;d<e;++d)if(c[d].zc(a,b))return!0;return!1};k.Nd=function(a){Qb(Infinity,Infinity,-Infinity,-Infinity,a);for(var b=this.f,c=0,d=b.length;c<d;++c)Vb(a,b[c].C());return a};k.df=function(){return sn(this.f)};
k.md=function(a){this.o!=this.g&&(Aa(this.i),this.l=0,this.o=this.g);if(0>a||0!==this.l&&a<this.l)return this;var b=a.toString();if(this.i.hasOwnProperty(b))return this.i[b];var c=[],d=this.f,e=!1,f,g;f=0;for(g=d.length;f<g;++f){var h=d[f],l=h.md(a);c.push(l);l!==h&&(e=!0)}if(e)return a=new qn(null),tn(a),a.f=c,rn(a),a.u(),this.i[b]=a;this.l=a;return this};k.X=function(){return"GeometryCollection"};k.La=function(a){var b=this.f,c,d;c=0;for(d=b.length;c<d;++c)if(b[c].La(a))return!0;return!1};
k.rotate=function(a,b){for(var c=this.f,d=0,e=c.length;d<e;++d)c[d].rotate(a,b);this.u()};k.scale=function(a,b,c){c||(c=gc(this.C()));for(var d=this.f,e=0,f=d.length;e<f;++e)d[e].scale(a,b,c);this.u()};k.ei=function(a){a=sn(a);tn(this);this.f=a;rn(this);this.u()};k.rc=function(a){var b=this.f,c,d;c=0;for(d=b.length;c<d;++c)b[c].rc(a);this.u()};k.Qc=function(a,b){var c=this.f,d,e;d=0;for(e=c.length;d<e;++d)c[d].Qc(a,b);this.u()};k.ma=function(){tn(this);Qc.prototype.ma.call(this)};function un(a){a=a?a:{};this.defaultDataProjection=null;this.defaultDataProjection=vc(a.defaultDataProjection?a.defaultDataProjection:"EPSG:4326");this.b=a.geometryName}w(un,an);function vn(a,b){return a?$m((0,wn[a.type])(a),!1,b):null}function xn(a,b){return(0,yn[a.X()])($m(a,!0,b),b)}
var wn={Point:function(a){return new C(a.coordinates)},LineString:function(a){return new P(a.coordinates)},Polygon:function(a){return new D(a.coordinates)},MultiPoint:function(a){return new R(a.coordinates)},MultiLineString:function(a){return new Q(a.coordinates)},MultiPolygon:function(a){return new S(a.coordinates)},GeometryCollection:function(a,b){var c=a.geometries.map(function(a){return vn(a,b)});return new qn(c)}},yn={Point:function(a){return{type:"Point",coordinates:a.Z()}},LineString:function(a){return{type:"LineString",
coordinates:a.Z()}},Polygon:function(a,b){var c;b&&(c=b.rightHanded);return{type:"Polygon",coordinates:a.Z(c)}},MultiPoint:function(a){return{type:"MultiPoint",coordinates:a.Z()}},MultiLineString:function(a){return{type:"MultiLineString",coordinates:a.Z()}},MultiPolygon:function(a,b){var c;b&&(c=b.rightHanded);return{type:"MultiPolygon",coordinates:a.Z(c)}},GeometryCollection:function(a,b){return{type:"GeometryCollection",geometries:a.f.map(function(a){var d=za({},b);delete d.featureProjection;return xn(a,
d)})}},Circle:function(){return{type:"GeometryCollection",geometries:[]}}};k=un.prototype;k.Tc=function(a,b){var c=vn(a.geometry,b),d=new Am;this.b&&d.Cc(this.b);d.Xa(c);void 0!==a.id&&d.Xb(a.id);a.properties&&d.G(a.properties);return d};k.If=function(a,b){var c;if("Feature"==a.type)c=[this.Tc(a,b)];else if("FeatureCollection"==a.type){c=[];var d=a.features,e,f;e=0;for(f=d.length;e<f;++e)c.push(this.Tc(d[e],b))}else la(!1,35);return c};k.Oh=function(a,b){return vn(a,b)};
k.Vh=function(a){a=a.crs;var b;a?"name"==a.type?b=vc(a.properties.name):"EPSG"==a.type?b=vc("EPSG:"+a.properties.code):la(!1,36):b=this.defaultDataProjection;return b};k.Xc=function(a,b){b=Zm(this,b);var c={type:"Feature"},d=a.Oa();void 0!==d&&(c.id=d);(d=a.Y())?c.geometry=xn(d,b):c.geometry=null;d=a.N();delete d[a.a];Ca(d)?c.properties=null:c.properties=d;return c};k.He=function(a,b){b=Zm(this,b);var c=[],d,e;d=0;for(e=a.length;d<e;++d)c.push(this.Xc(a[d],b));return{type:"FeatureCollection",features:c}};
k.Ie=function(a,b){return xn(a,Zm(this,b))};function zn(){this.f=new XMLSerializer;this.defaultDataProjection=null}w(zn,Xm);k=zn.prototype;k.X=function(){return"xml"};k.Vb=function(a,b){if(Gm(a))return An(this,a,b);if(Hm(a))return this.Mh(a,b);if("string"===typeof a){var c=Im(a);return An(this,c,b)}return null};function An(a,b,c){a=Bn(a,b,c);return 0<a.length?a[0]:null}k.Fa=function(a,b){if(Gm(a))return Bn(this,a,b);if(Hm(a))return this.nc(a,b);if("string"===typeof a){var c=Im(a);return Bn(this,c,b)}return[]};
function Bn(a,b,c){var d=[];for(b=b.firstChild;b;b=b.nextSibling)b.nodeType==Node.ELEMENT_NODE&&eb(d,a.nc(b,c));return d}k.Uc=function(a,b){if(Gm(a))return this.v(a,b);if(Hm(a)){var c=this.xe(a,[Ym(this,a,b?b:{})]);return c?c:null}return"string"===typeof a?(c=Im(a),this.v(c,b)):null};k.Ra=function(a){return Gm(a)?this.Of(a):Hm(a)?this.Ae(a):"string"===typeof a?(a=Im(a),this.Of(a)):null};k.Of=function(){return this.defaultDataProjection};k.Ae=function(){return this.defaultDataProjection};
k.Cd=function(a,b){var c=this.A(a,b);return this.f.serializeToString(c)};k.ac=function(a,b){var c=this.a(a,b);return this.f.serializeToString(c)};k.Yc=function(a,b){var c=this.s(a,b);return this.f.serializeToString(c)};function Cn(a){a=a?a:{};this.featureType=a.featureType;this.featureNS=a.featureNS;this.srsName=a.srsName;this.schemaLocation="";this.b={};this.b["http://www.opengis.net/gml"]={featureMember:Lm(Cn.prototype.ud),featureMembers:Lm(Cn.prototype.ud)};zn.call(this)}w(Cn,zn);var Dn=/^[\s\xa0]*$/;k=Cn.prototype;
k.ud=function(a,b){var c=a.localName,d=null;if("FeatureCollection"==c)"http://www.opengis.net/wfs"===a.namespaceURI?d=O([],this.b,a,b,this):d=O(null,this.b,a,b,this);else if("featureMembers"==c||"featureMember"==c){var e=b[0],f=e.featureType,g=e.featureNS,h,l;if(!f&&a.childNodes){f=[];g={};h=0;for(l=a.childNodes.length;h<l;++h){var m=a.childNodes[h];if(1===m.nodeType){var n=m.nodeName.split(":").pop();if(-1===f.indexOf(n)){var p="",q=0,m=m.namespaceURI,t;for(t in g){if(g[t]===m){p=t;break}++q}p||
(p="p"+q,g[p]=m);f.push(p+":"+n)}}}"featureMember"!=c&&(e.featureType=f,e.featureNS=g)}"string"===typeof g&&(h=g,g={},g.p0=h);var e={},f=Array.isArray(f)?f:[f],v;for(v in g){n={};h=0;for(l=f.length;h<l;++h)(-1===f[h].indexOf(":")?"p0":f[h].split(":")[0])===v&&(n[f[h].split(":").pop()]="featureMembers"==c?Km(this.Hf,this):Lm(this.Hf,this));e[g[v]]=n}"featureMember"==c?d=O(void 0,e,a,b):d=O([],e,a,b)}null===d&&(d=[]);return d};
k.xe=function(a,b){var c=b[0];c.srsName=a.firstElementChild.getAttribute("srsName");var d=O(null,this.ag,a,b,this);if(d)return $m(d,!1,c)};
k.Hf=function(a,b){var c,d;(d=a.getAttribute("fid"))||(d=a.getAttributeNS("http://www.opengis.net/gml","id")||"");var e={},f;for(c=a.firstElementChild;c;c=c.nextElementSibling){var g=c.localName;if(0===c.childNodes.length||1===c.childNodes.length&&(3===c.firstChild.nodeType||4===c.firstChild.nodeType)){var h=Em(c,!1);Dn.test(h)&&(h=void 0);e[g]=h}else"boundedBy"!==g&&(f=g),e[g]=this.xe(c,b)}c=new Am(e);f&&c.Cc(f);d&&c.Xb(d);return c};
k.Uh=function(a,b){var c=this.we(a,b);if(c){var d=new C(null);d.ba("XYZ",c);return d}};k.Sh=function(a,b){var c=O([],this.Qi,a,b,this);if(c)return new R(c)};k.Rh=function(a,b){var c=O([],this.Pi,a,b,this);if(c){var d=new Q(null);fn(d,c);return d}};k.Th=function(a,b){var c=O([],this.Ri,a,b,this);if(c){var d=new S(null);hn(d,c);return d}};k.Jh=function(a,b){Sm(this.Ui,a,b,this)};k.Rg=function(a,b){Sm(this.Ni,a,b,this)};k.Kh=function(a,b){Sm(this.Vi,a,b,this)};
k.ye=function(a,b){var c=this.we(a,b);if(c){var d=new P(null);d.ba("XYZ",c);return d}};k.wo=function(a,b){var c=O(null,this.Ed,a,b,this);if(c)return c};k.Qh=function(a,b){var c=this.we(a,b);if(c){var d=new jd(null);kd(d,"XYZ",c);return d}};k.ze=function(a,b){var c=O([null],this.Le,a,b,this);if(c&&c[0]){var d=new D(null),e=c[0],f=[e.length],g,h;g=1;for(h=c.length;g<h;++g)eb(e,c[g]),f.push(e.length);d.ba("XYZ",e,f);return d}};k.we=function(a,b){return O(null,this.Ed,a,b,this)};
k.Qi={"http://www.opengis.net/gml":{pointMember:Km(Cn.prototype.Jh),pointMembers:Km(Cn.prototype.Jh)}};k.Pi={"http://www.opengis.net/gml":{lineStringMember:Km(Cn.prototype.Rg),lineStringMembers:Km(Cn.prototype.Rg)}};k.Ri={"http://www.opengis.net/gml":{polygonMember:Km(Cn.prototype.Kh),polygonMembers:Km(Cn.prototype.Kh)}};k.Ui={"http://www.opengis.net/gml":{Point:Km(Cn.prototype.we)}};k.Ni={"http://www.opengis.net/gml":{LineString:Km(Cn.prototype.ye)}};k.Vi={"http://www.opengis.net/gml":{Polygon:Km(Cn.prototype.ze)}};
k.Fd={"http://www.opengis.net/gml":{LinearRing:Lm(Cn.prototype.wo)}};k.nc=function(a,b){var c={featureType:this.featureType,featureNS:this.featureNS};b&&za(c,Ym(this,a,b));return this.ud(a,[c])||[]};k.Ae=function(a){return vc(this.srsName?this.srsName:a.firstElementChild.getAttribute("srsName"))};function En(a){a=Em(a,!1);return Fn(a)}function Fn(a){if(a=/^\s*(true|1)|(false|0)\s*$/.exec(a))return void 0!==a[1]||!1}function Gn(a){a=Em(a,!1);a=Date.parse(a);return isNaN(a)?void 0:a/1E3}function Hn(a){a=Em(a,!1);return In(a)}function In(a){if(a=/^\s*([+\-]?\d*\.?\d+(?:e[+\-]?\d+)?)\s*$/i.exec(a))return parseFloat(a[1])}function Jn(a){a=Em(a,!1);return Kn(a)}function Kn(a){if(a=/^\s*(\d+)\s*$/.exec(a))return parseInt(a[1],10)}function T(a){return Em(a,!1).trim()}
function Ln(a,b){Mn(a,b?"1":"0")}function Nn(a,b){a.appendChild(Cm.createTextNode(b.toPrecision()))}function On(a,b){a.appendChild(Cm.createTextNode(b.toString()))}function Mn(a,b){a.appendChild(Cm.createTextNode(b))};function Pn(a){a=a?a:{};Cn.call(this,a);this.o=void 0!==a.surface?a.surface:!1;this.i=void 0!==a.curve?a.curve:!1;this.l=void 0!==a.multiCurve?a.multiCurve:!0;this.j=void 0!==a.multiSurface?a.multiSurface:!0;this.schemaLocation=a.schemaLocation?a.schemaLocation:"http://www.opengis.net/gml http://schemas.opengis.net/gml/3.1.1/profiles/gmlsfProfile/1.0.0/gmlsf.xsd"}w(Pn,Cn);k=Pn.prototype;k.Ao=function(a,b){var c=O([],this.Oi,a,b,this);if(c){var d=new Q(null);fn(d,c);return d}};
k.Bo=function(a,b){var c=O([],this.Si,a,b,this);if(c){var d=new S(null);hn(d,c);return d}};k.tg=function(a,b){Sm(this.Ki,a,b,this)};k.si=function(a,b){Sm(this.Zi,a,b,this)};k.Eo=function(a,b){return O([null],this.Ti,a,b,this)};k.Go=function(a,b){return O([null],this.Yi,a,b,this)};k.Fo=function(a,b){return O([null],this.Le,a,b,this)};k.zo=function(a,b){return O([null],this.Ed,a,b,this)};k.jl=function(a,b){var c=O(void 0,this.Fd,a,b,this);c&&b[b.length-1].push(c)};
k.Ej=function(a,b){var c=O(void 0,this.Fd,a,b,this);c&&(b[b.length-1][0]=c)};k.Wh=function(a,b){var c=O([null],this.$i,a,b,this);if(c&&c[0]){var d=new D(null),e=c[0],f=[e.length],g,h;g=1;for(h=c.length;g<h;++g)eb(e,c[g]),f.push(e.length);d.ba("XYZ",e,f);return d}};k.Lh=function(a,b){var c=O([null],this.Li,a,b,this);if(c){var d=new P(null);d.ba("XYZ",c);return d}};k.vo=function(a,b){var c=O([null],this.Mi,a,b,this);return Qb(c[1][0],c[1][1],c[2][0],c[2][1])};
k.xo=function(a,b){for(var c=Em(a,!1),d=/^\s*([+\-]?\d*\.?\d+(?:[eE][+\-]?\d+)?)\s*/,e=[],f;f=d.exec(c);)e.push(parseFloat(f[1])),c=c.substr(f[0].length);if(""===c){c=b[0].srsName;d="enu";c&&(d=vc(c).b);if("neu"===d)for(c=0,d=e.length;c<d;c+=3)f=e[c],e[c]=e[c+1],e[c+1]=f;c=e.length;2==c&&e.push(0);return 0===c?void 0:e}};
k.Lf=function(a,b){var c=Em(a,!1).replace(/^\s*|\s*$/g,""),d=b[0].srsName,e=a.parentNode.getAttribute("srsDimension"),f="enu";d&&(f=vc(d).b);c=c.split(/\s+/);d=2;a.getAttribute("srsDimension")?d=Kn(a.getAttribute("srsDimension")):a.getAttribute("dimension")?d=Kn(a.getAttribute("dimension")):e&&(d=Kn(e));for(var g,h,l=[],m=0,n=c.length;m<n;m+=d)e=parseFloat(c[m]),g=parseFloat(c[m+1]),h=3===d?parseFloat(c[m+2]):0,"en"===f.substr(0,2)?l.push(e,g,h):l.push(g,e,h);return l};
k.Ed={"http://www.opengis.net/gml":{pos:Lm(Pn.prototype.xo),posList:Lm(Pn.prototype.Lf)}};k.Le={"http://www.opengis.net/gml":{interior:Pn.prototype.jl,exterior:Pn.prototype.Ej}};
k.ag={"http://www.opengis.net/gml":{Point:Lm(Cn.prototype.Uh),MultiPoint:Lm(Cn.prototype.Sh),LineString:Lm(Cn.prototype.ye),MultiLineString:Lm(Cn.prototype.Rh),LinearRing:Lm(Cn.prototype.Qh),Polygon:Lm(Cn.prototype.ze),MultiPolygon:Lm(Cn.prototype.Th),Surface:Lm(Pn.prototype.Wh),MultiSurface:Lm(Pn.prototype.Bo),Curve:Lm(Pn.prototype.Lh),MultiCurve:Lm(Pn.prototype.Ao),Envelope:Lm(Pn.prototype.vo)}};k.Oi={"http://www.opengis.net/gml":{curveMember:Km(Pn.prototype.tg),curveMembers:Km(Pn.prototype.tg)}};
k.Si={"http://www.opengis.net/gml":{surfaceMember:Km(Pn.prototype.si),surfaceMembers:Km(Pn.prototype.si)}};k.Ki={"http://www.opengis.net/gml":{LineString:Km(Cn.prototype.ye),Curve:Km(Pn.prototype.Lh)}};k.Zi={"http://www.opengis.net/gml":{Polygon:Km(Cn.prototype.ze),Surface:Km(Pn.prototype.Wh)}};k.$i={"http://www.opengis.net/gml":{patches:Lm(Pn.prototype.Eo)}};k.Li={"http://www.opengis.net/gml":{segments:Lm(Pn.prototype.Go)}};k.Mi={"http://www.opengis.net/gml":{lowerCorner:Km(Pn.prototype.Lf),upperCorner:Km(Pn.prototype.Lf)}};
k.Ti={"http://www.opengis.net/gml":{PolygonPatch:Lm(Pn.prototype.Fo)}};k.Yi={"http://www.opengis.net/gml":{LineStringSegment:Lm(Pn.prototype.zo)}};function Qn(a,b,c){c=c[c.length-1].srsName;b=b.Z();for(var d=b.length,e=Array(d),f,g=0;g<d;++g){f=b[g];var h=g,l="enu";c&&(l=vc(c).b);e[h]="en"===l.substr(0,2)?f[0]+" "+f[1]:f[1]+" "+f[0]}Mn(a,e.join(" "))}
k.Gi=function(a,b,c){var d=c[c.length-1].srsName;d&&a.setAttribute("srsName",d);d=Dm(a.namespaceURI,"pos");a.appendChild(d);c=c[c.length-1].srsName;a="enu";c&&(a=vc(c).b);b=b.Z();Mn(d,"en"===a.substr(0,2)?b[0]+" "+b[1]:b[1]+" "+b[0])};var Rn={"http://www.opengis.net/gml":{lowerCorner:L(Mn),upperCorner:L(Mn)}};k=Pn.prototype;k.up=function(a,b,c){var d=c[c.length-1].srsName;d&&a.setAttribute("srsName",d);Tm({node:a},Rn,Qm,[b[0]+" "+b[1],b[2]+" "+b[3]],c,["lowerCorner","upperCorner"],this)};
k.Di=function(a,b,c){var d=c[c.length-1].srsName;d&&a.setAttribute("srsName",d);d=Dm(a.namespaceURI,"posList");a.appendChild(d);Qn(d,b,c)};k.Xi=function(a,b){var c=b[b.length-1],d=c.node,e=c.exteriorWritten;void 0===e&&(c.exteriorWritten=!0);return Dm(d.namespaceURI,void 0!==e?"interior":"exterior")};
k.Je=function(a,b,c){var d=c[c.length-1].srsName;"PolygonPatch"!==a.nodeName&&d&&a.setAttribute("srsName",d);"Polygon"===a.nodeName||"PolygonPatch"===a.nodeName?(b=b.Vd(),Tm({node:a,srsName:d},Sn,this.Xi,b,c,void 0,this)):"Surface"===a.nodeName&&(d=Dm(a.namespaceURI,"patches"),a.appendChild(d),a=Dm(d.namespaceURI,"PolygonPatch"),d.appendChild(a),this.Je(a,b,c))};
k.Fe=function(a,b,c){var d=c[c.length-1].srsName;"LineStringSegment"!==a.nodeName&&d&&a.setAttribute("srsName",d);"LineString"===a.nodeName||"LineStringSegment"===a.nodeName?(d=Dm(a.namespaceURI,"posList"),a.appendChild(d),Qn(d,b,c)):"Curve"===a.nodeName&&(d=Dm(a.namespaceURI,"segments"),a.appendChild(d),a=Dm(d.namespaceURI,"LineStringSegment"),d.appendChild(a),this.Fe(a,b,c))};
k.Fi=function(a,b,c){var d=c[c.length-1],e=d.srsName,d=d.surface;e&&a.setAttribute("srsName",e);b=b.Wd();Tm({node:a,srsName:e,surface:d},Tn,this.c,b,c,void 0,this)};k.vp=function(a,b,c){var d=c[c.length-1].srsName;d&&a.setAttribute("srsName",d);b=b.he();Tm({node:a,srsName:d},Un,Om("pointMember"),b,c,void 0,this)};k.Ei=function(a,b,c){var d=c[c.length-1],e=d.srsName,d=d.curve;e&&a.setAttribute("srsName",e);b=b.ld();Tm({node:a,srsName:e,curve:d},Vn,this.c,b,c,void 0,this)};
k.Hi=function(a,b,c){var d=Dm(a.namespaceURI,"LinearRing");a.appendChild(d);this.Di(d,b,c)};k.Ii=function(a,b,c){var d=this.g(b,c);d&&(a.appendChild(d),this.Je(d,b,c))};k.wp=function(a,b,c){var d=Dm(a.namespaceURI,"Point");a.appendChild(d);this.Gi(d,b,c)};k.Ci=function(a,b,c){var d=this.g(b,c);d&&(a.appendChild(d),this.Fe(d,b,c))};
k.Zc=function(a,b,c){var d=c[c.length-1],e=za({},d);e.node=a;var f;Array.isArray(b)?d.dataProjection?f=Pc(b,d.featureProjection,d.dataProjection):f=b:f=$m(b,!0,d);Tm(e,Wn,this.g,[f],c,void 0,this)};
k.yi=function(a,b,c){var d=b.Oa();d&&a.setAttribute("fid",d);var d=c[c.length-1],e=d.featureNS,f=b.a;d.Bc||(d.Bc={},d.Bc[e]={});var g=b.N();b=[];var h=[],l;for(l in g){var m=g[l];null!==m&&(b.push(l),h.push(m),l==f||m instanceof Qc?l in d.Bc[e]||(d.Bc[e][l]=L(this.Zc,this)):l in d.Bc[e]||(d.Bc[e][l]=L(Mn)))}l=za({},d);l.node=a;Tm(l,d.Bc,Om(void 0,e),h,c,b)};
var Tn={"http://www.opengis.net/gml":{surfaceMember:L(Pn.prototype.Ii),polygonMember:L(Pn.prototype.Ii)}},Un={"http://www.opengis.net/gml":{pointMember:L(Pn.prototype.wp)}},Vn={"http://www.opengis.net/gml":{lineStringMember:L(Pn.prototype.Ci),curveMember:L(Pn.prototype.Ci)}},Sn={"http://www.opengis.net/gml":{exterior:L(Pn.prototype.Hi),interior:L(Pn.prototype.Hi)}},Wn={"http://www.opengis.net/gml":{Curve:L(Pn.prototype.Fe),MultiCurve:L(Pn.prototype.Ei),Point:L(Pn.prototype.Gi),MultiPoint:L(Pn.prototype.vp),
LineString:L(Pn.prototype.Fe),MultiLineString:L(Pn.prototype.Ei),LinearRing:L(Pn.prototype.Di),Polygon:L(Pn.prototype.Je),MultiPolygon:L(Pn.prototype.Fi),Surface:L(Pn.prototype.Je),MultiSurface:L(Pn.prototype.Fi),Envelope:L(Pn.prototype.up)}},Xn={MultiLineString:"lineStringMember",MultiCurve:"curveMember",MultiPolygon:"polygonMember",MultiSurface:"surfaceMember"};Pn.prototype.c=function(a,b){return Dm("http://www.opengis.net/gml",Xn[b[b.length-1].node.nodeName])};
Pn.prototype.g=function(a,b){var c=b[b.length-1],d=c.multiSurface,e=c.surface,f=c.curve,c=c.multiCurve,g;Array.isArray(a)?g="Envelope":(g=a.X(),"MultiPolygon"===g&&!0===d?g="MultiSurface":"Polygon"===g&&!0===e?g="Surface":"LineString"===g&&!0===f?g="Curve":"MultiLineString"===g&&!0===c&&(g="MultiCurve"));return Dm("http://www.opengis.net/gml",g)};
Pn.prototype.s=function(a,b){b=Zm(this,b);var c=Dm("http://www.opengis.net/gml","geom"),d={node:c,srsName:this.srsName,curve:this.i,surface:this.o,multiSurface:this.j,multiCurve:this.l};b&&za(d,b);this.Zc(c,a,[d]);return c};
Pn.prototype.a=function(a,b){b=Zm(this,b);var c=Dm("http://www.opengis.net/gml","featureMembers");c.setAttributeNS("http://www.w3.org/2001/XMLSchema-instance","xsi:schemaLocation",this.schemaLocation);var d={srsName:this.srsName,curve:this.i,surface:this.o,multiSurface:this.j,multiCurve:this.l,featureNS:this.featureNS,featureType:this.featureType};b&&za(d,b);var d=[d],e=d[d.length-1],f=e.featureType,g=e.featureNS,h={};h[g]={};h[g][f]=L(this.yi,this);e=za({},e);e.node=c;Tm(e,h,Om(f,g),a,d);return c};function Yn(a){a=a?a:{};Cn.call(this,a);this.b["http://www.opengis.net/gml"].featureMember=Km(Cn.prototype.ud);this.schemaLocation=a.schemaLocation?a.schemaLocation:"http://www.opengis.net/gml http://schemas.opengis.net/gml/2.1.2/feature.xsd"}w(Yn,Cn);k=Yn.prototype;
k.Nh=function(a,b){var c=Em(a,!1).replace(/^\s*|\s*$/g,""),d=b[0].srsName,e=a.parentNode.getAttribute("srsDimension"),f="enu";d&&(d=vc(d))&&(f=d.b);c=c.split(/[\s,]+/);d=2;a.getAttribute("srsDimension")?d=Kn(a.getAttribute("srsDimension")):a.getAttribute("dimension")?d=Kn(a.getAttribute("dimension")):e&&(d=Kn(e));for(var g,h,l=[],m=0,n=c.length;m<n;m+=d)e=parseFloat(c[m]),g=parseFloat(c[m+1]),h=3===d?parseFloat(c[m+2]):0,"en"===f.substr(0,2)?l.push(e,g,h):l.push(g,e,h);return l};
k.to=function(a,b){var c=O([null],this.Ji,a,b,this);return Qb(c[1][0],c[1][1],c[1][3],c[1][4])};k.hl=function(a,b){var c=O(void 0,this.Fd,a,b,this);c&&b[b.length-1].push(c)};k.ao=function(a,b){var c=O(void 0,this.Fd,a,b,this);c&&(b[b.length-1][0]=c)};k.Ed={"http://www.opengis.net/gml":{coordinates:Lm(Yn.prototype.Nh)}};k.Le={"http://www.opengis.net/gml":{innerBoundaryIs:Yn.prototype.hl,outerBoundaryIs:Yn.prototype.ao}};k.Ji={"http://www.opengis.net/gml":{coordinates:Km(Yn.prototype.Nh)}};
k.ag={"http://www.opengis.net/gml":{Point:Lm(Cn.prototype.Uh),MultiPoint:Lm(Cn.prototype.Sh),LineString:Lm(Cn.prototype.ye),MultiLineString:Lm(Cn.prototype.Rh),LinearRing:Lm(Cn.prototype.Qh),Polygon:Lm(Cn.prototype.ze),MultiPolygon:Lm(Cn.prototype.Th),Box:Lm(Yn.prototype.to)}};function Zn(a){a=a?a:{};zn.call(this);this.defaultDataProjection=vc("EPSG:4326");this.b=a.readExtensions}w(Zn,zn);var $n=[null,"http://www.topografix.com/GPX/1/0","http://www.topografix.com/GPX/1/1"];function ao(a,b,c){a.push(parseFloat(b.getAttribute("lon")),parseFloat(b.getAttribute("lat")));"ele"in c?(a.push(c.ele),delete c.ele):a.push(0);"time"in c?(a.push(c.time),delete c.time):a.push(0);return a}function bo(a,b){var c=b[b.length-1],d=a.getAttribute("href");null!==d&&(c.link=d);Sm(co,a,b)}
function eo(a,b){b[b.length-1].extensionsNode_=a}function fo(a,b){var c=b[0],d=O({flatCoordinates:[]},go,a,b);if(d){var e=d.flatCoordinates;delete d.flatCoordinates;var f=new P(null);f.ba("XYZM",e);$m(f,!1,c);c=new Am(f);c.G(d);return c}}function ho(a,b){var c=b[0],d=O({flatCoordinates:[],ends:[]},io,a,b);if(d){var e=d.flatCoordinates;delete d.flatCoordinates;var f=d.ends;delete d.ends;var g=new Q(null);g.ba("XYZM",e,f);$m(g,!1,c);c=new Am(g);c.G(d);return c}}
function jo(a,b){var c=b[0],d=O({},ko,a,b);if(d){var e=ao([],a,d),e=new C(e,"XYZM");$m(e,!1,c);c=new Am(e);c.G(d);return c}}
var lo={rte:fo,trk:ho,wpt:jo},mo=N($n,{rte:Km(fo),trk:Km(ho),wpt:Km(jo)}),co=N($n,{text:K(T,"linkText"),type:K(T,"linkType")}),go=N($n,{name:K(T),cmt:K(T),desc:K(T),src:K(T),link:bo,number:K(Jn),extensions:eo,type:K(T),rtept:function(a,b){var c=O({},no,a,b);c&&ao(b[b.length-1].flatCoordinates,a,c)}}),no=N($n,{ele:K(Hn),time:K(Gn)}),io=N($n,{name:K(T),cmt:K(T),desc:K(T),src:K(T),link:bo,number:K(Jn),type:K(T),extensions:eo,trkseg:function(a,b){var c=b[b.length-1];Sm(oo,a,b);c.ends.push(c.flatCoordinates.length)}}),
oo=N($n,{trkpt:function(a,b){var c=O({},po,a,b);c&&ao(b[b.length-1].flatCoordinates,a,c)}}),po=N($n,{ele:K(Hn),time:K(Gn)}),ko=N($n,{ele:K(Hn),time:K(Gn),magvar:K(Hn),geoidheight:K(Hn),name:K(T),cmt:K(T),desc:K(T),src:K(T),link:bo,sym:K(T),type:K(T),fix:K(T),sat:K(Jn),hdop:K(Hn),vdop:K(Hn),pdop:K(Hn),ageofdgpsdata:K(Hn),dgpsid:K(Jn),extensions:eo});
function qo(a,b){b||(b=[]);for(var c=0,d=b.length;c<d;++c){var e=b[c];if(a.b){var f=e.get("extensionsNode_")||null;a.b(e,f)}e.set("extensionsNode_",void 0)}}Zn.prototype.Mh=function(a,b){if(!bb($n,a.namespaceURI))return null;var c=lo[a.localName];if(!c)return null;c=c(a,[Ym(this,a,b)]);if(!c)return null;qo(this,[c]);return c};Zn.prototype.nc=function(a,b){if(!bb($n,a.namespaceURI))return[];if("gpx"==a.localName){var c=O([],mo,a,[Ym(this,a,b)]);if(c)return qo(this,c),c}return[]};
function ro(a,b,c){a.setAttribute("href",b);b=c[c.length-1].properties;Tm({node:a},so,Qm,[b.linkText,b.linkType],c,to)}function uo(a,b,c){var d=c[c.length-1],e=d.node.namespaceURI,f=d.properties;a.setAttributeNS(null,"lat",b[1]);a.setAttributeNS(null,"lon",b[0]);switch(d.geometryLayout){case "XYZM":0!==b[3]&&(f.time=b[3]);case "XYZ":0!==b[2]&&(f.ele=b[2]);break;case "XYM":0!==b[2]&&(f.time=b[2])}b="rtept"==a.nodeName?vo[e]:wo[e];d=Rm(f,b);Tm({node:a,properties:f},xo,Qm,d,c,b)}
var to=["text","type"],so=N($n,{text:L(Mn),type:L(Mn)}),yo=N($n,"name cmt desc src link number type rtept".split(" ")),zo=N($n,{name:L(Mn),cmt:L(Mn),desc:L(Mn),src:L(Mn),link:L(ro),number:L(On),type:L(Mn),rtept:Nm(L(uo))}),vo=N($n,["ele","time"]),Ao=N($n,"name cmt desc src link number type trkseg".split(" ")),Do=N($n,{name:L(Mn),cmt:L(Mn),desc:L(Mn),src:L(Mn),link:L(ro),number:L(On),type:L(Mn),trkseg:Nm(L(function(a,b,c){Tm({node:a,geometryLayout:b.ka,properties:{}},Bo,Co,b.Z(),c)}))}),Co=Om("trkpt"),
Bo=N($n,{trkpt:L(uo)}),wo=N($n,"ele time magvar geoidheight name cmt desc src link sym type fix sat hdop vdop pdop ageofdgpsdata dgpsid".split(" ")),xo=N($n,{ele:L(Nn),time:L(function(a,b){var c=new Date(1E3*b);a.appendChild(Cm.createTextNode(c.getUTCFullYear()+"-"+sb(c.getUTCMonth()+1)+"-"+sb(c.getUTCDate())+"T"+sb(c.getUTCHours())+":"+sb(c.getUTCMinutes())+":"+sb(c.getUTCSeconds())+"Z"))}),magvar:L(Nn),geoidheight:L(Nn),name:L(Mn),cmt:L(Mn),desc:L(Mn),src:L(Mn),link:L(ro),sym:L(Mn),type:L(Mn),fix:L(Mn),
sat:L(On),hdop:L(Nn),vdop:L(Nn),pdop:L(Nn),ageofdgpsdata:L(Nn),dgpsid:L(On)}),Eo={Point:"wpt",LineString:"rte",MultiLineString:"trk"};function Fo(a,b){var c=a.Y();if(c&&(c=Eo[c.X()]))return Dm(b[b.length-1].node.namespaceURI,c)}
var Go=N($n,{rte:L(function(a,b,c){var d=c[0],e=b.N();a={node:a,properties:e};if(b=b.Y())b=$m(b,!0,d),a.geometryLayout=b.ka,e.rtept=b.Z();d=yo[c[c.length-1].node.namespaceURI];e=Rm(e,d);Tm(a,zo,Qm,e,c,d)}),trk:L(function(a,b,c){var d=c[0],e=b.N();a={node:a,properties:e};if(b=b.Y())b=$m(b,!0,d),e.trkseg=b.ld();d=Ao[c[c.length-1].node.namespaceURI];e=Rm(e,d);Tm(a,Do,Qm,e,c,d)}),wpt:L(function(a,b,c){var d=c[0],e=c[c.length-1];e.properties=b.N();if(b=b.Y())b=$m(b,!0,d),e.geometryLayout=b.ka,uo(a,b.Z(),
c)})});Zn.prototype.a=function(a,b){b=Zm(this,b);var c=Dm("http://www.topografix.com/GPX/1/1","gpx");c.setAttributeNS("http://www.w3.org/2000/xmlns/","xmlns:xsi","http://www.w3.org/2001/XMLSchema-instance");c.setAttributeNS("http://www.w3.org/2001/XMLSchema-instance","xsi:schemaLocation","http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd");c.setAttribute("version","1.1");c.setAttribute("creator","OpenLayers 3");Tm({node:c},Go,Fo,a,[b]);return c};function Ho(){this.defaultDataProjection=null}w(Ho,Xm);function Io(a){return"string"===typeof a?a:""}k=Ho.prototype;k.X=function(){return"text"};k.Vb=function(a,b){return this.td(Io(a),Zm(this,b))};k.Fa=function(a,b){return this.Jf(Io(a),Zm(this,b))};k.Uc=function(a,b){return this.vd(Io(a),Zm(this,b))};k.Ra=function(){return this.defaultDataProjection};k.Cd=function(a,b){return this.Ge(a,Zm(this,b))};k.ac=function(a,b){return this.zi(a,Zm(this,b))};k.Yc=function(a,b){return this.Dd(a,Zm(this,b))};function Jo(a){a=a?a:{};this.defaultDataProjection=null;this.defaultDataProjection=vc("EPSG:4326");this.b=a.altitudeMode?a.altitudeMode:"none"}w(Jo,Ho);var Ko=/^B(\d{2})(\d{2})(\d{2})(\d{2})(\d{5})([NS])(\d{3})(\d{5})([EW])([AV])(\d{5})(\d{5})/,Lo=/^H.([A-Z]{3}).*?:(.*)/,Mo=/^HFDTE(\d{2})(\d{2})(\d{2})/,No=/\r\n|\r|\n/;
Jo.prototype.td=function(a,b){var c=this.b,d=a.split(No),e={},f=[],g=2E3,h=0,l=1,m=-1,n,p;n=0;for(p=d.length;n<p;++n){var q=d[n],t;if("B"==q.charAt(0)){if(t=Ko.exec(q)){var q=parseInt(t[1],10),v=parseInt(t[2],10),u=parseInt(t[3],10),y=parseInt(t[4],10)+parseInt(t[5],10)/6E4;"S"==t[6]&&(y=-y);var E=parseInt(t[7],10)+parseInt(t[8],10)/6E4;"W"==t[9]&&(E=-E);f.push(E,y);"none"!=c&&f.push("gps"==c?parseInt(t[11],10):"barometric"==c?parseInt(t[12],10):0);t=Date.UTC(g,h,l,q,v,u);t<m&&(t=Date.UTC(g,h,l+1,
q,v,u));f.push(t/1E3);m=t}}else"H"==q.charAt(0)&&((t=Mo.exec(q))?(l=parseInt(t[1],10),h=parseInt(t[2],10)-1,g=2E3+parseInt(t[3],10)):(t=Lo.exec(q))&&(e[t[1]]=t[2].trim()))}if(0===f.length)return null;d=new P(null);d.ba("none"==c?"XYM":"XYZM",f);c=new Am($m(d,!1,b));c.G(e);return c};Jo.prototype.Jf=function(a,b){var c=this.td(a,b);return c?[c]:[]};function Oo(a,b,c,d,e,f){Sa.call(this);this.j=null;this.a=a?a:new Image;null!==d&&(this.a.crossOrigin=d);this.c=f?document.createElement("CANVAS"):null;this.l=f;this.i=null;this.f=e;this.g=c;this.o=b;this.s=!1;2==this.f&&Po(this)}w(Oo,Sa);function Po(a){var b=$e(1,1);try{b.drawImage(a.a,0,0),b.getImageData(0,0,1,1)}catch(c){a.s=!0}}Oo.prototype.v=function(){this.f=3;this.i.forEach(Ea);this.i=null;this.b("change")};
Oo.prototype.U=function(){this.f=2;this.g&&(this.a.width=this.g[0],this.a.height=this.g[1]);this.g=[this.a.width,this.a.height];this.i.forEach(Ea);this.i=null;Po(this);if(!this.s&&null!==this.l){this.c.width=this.a.width;this.c.height=this.a.height;var a=this.c.getContext("2d");a.drawImage(this.a,0,0);for(var b=a.getImageData(0,0,this.a.width,this.a.height),c=b.data,d=this.l[0]/255,e=this.l[1]/255,f=this.l[2]/255,g=0,h=c.length;g<h;g+=4)c[g]*=d,c[g+1]*=e,c[g+2]*=f;a.putImageData(b,0,0)}this.b("change")};
Oo.prototype.load=function(){if(0==this.f){this.f=1;this.i=[Ja(this.a,"error",this.v,this),Ja(this.a,"load",this.U,this)];try{this.a.src=this.o}catch(a){this.v()}}};function Qo(a){a=a||{};this.c=void 0!==a.anchor?a.anchor:[.5,.5];this.f=null;this.a=void 0!==a.anchorOrigin?a.anchorOrigin:"top-left";this.j=void 0!==a.anchorXUnits?a.anchorXUnits:"fraction";this.s=void 0!==a.anchorYUnits?a.anchorYUnits:"fraction";var b=void 0!==a.crossOrigin?a.crossOrigin:null,c=void 0!==a.img?a.img:null,d=void 0!==a.imgSize?a.imgSize:null,e=a.src;la(!(void 0!==e&&c),4);la(!c||c&&d,5);void 0!==e&&0!==e.length||!c||(e=c.src||x(c).toString());la(void 0!==e&&0<e.length,6);var f=void 0!==
a.src?0:2,g=void 0!==a.color?Ae(a.color):null,h=Zg.get(e,b,g);h||(h=new Oo(c,e,d,b,f,g),Zg.set(e,b,g,h));this.b=h;this.D=void 0!==a.offset?a.offset:[0,0];this.g=void 0!==a.offsetOrigin?a.offsetOrigin:"top-left";this.l=null;this.A=void 0!==a.size?a.size:null;Pi.call(this,{opacity:void 0!==a.opacity?a.opacity:1,rotation:void 0!==a.rotation?a.rotation:0,scale:void 0!==a.scale?a.scale:1,snapToPixel:void 0!==a.snapToPixel?a.snapToPixel:!0,rotateWithView:void 0!==a.rotateWithView?a.rotateWithView:!1})}
w(Qo,Pi);k=Qo.prototype;k.bc=function(){if(this.f)return this.f;var a=this.c,b=this.Jb();if("fraction"==this.j||"fraction"==this.s){if(!b)return null;a=this.c.slice();"fraction"==this.j&&(a[0]*=b[0]);"fraction"==this.s&&(a[1]*=b[1])}if("top-left"!=this.a){if(!b)return null;a===this.c&&(a=this.c.slice());if("top-right"==this.a||"bottom-right"==this.a)a[0]=-a[0]+b[0];if("bottom-left"==this.a||"bottom-right"==this.a)a[1]=-a[1]+b[1]}return this.f=a};k.mc=function(){var a=this.b;return a.c?a.c:a.a};
k.kd=function(){return this.b.g};k.sd=function(){return this.b.f};k.ne=function(){var a=this.b;if(!a.j)if(a.s){var b=a.g[0],c=a.g[1],d=$e(b,c);d.fillRect(0,0,b,c);a.j=d.canvas}else a.j=a.a;return a.j};k.Ka=function(){if(this.l)return this.l;var a=this.D;if("top-left"!=this.g){var b=this.Jb(),c=this.b.g;if(!b||!c)return null;a=a.slice();if("top-right"==this.g||"bottom-right"==this.g)a[0]=c[0]-b[0]-a[0];if("bottom-left"==this.g||"bottom-right"==this.g)a[1]=c[1]-b[1]-a[1]}return this.l=a};k.zn=function(){return this.b.o};
k.Jb=function(){return this.A?this.A:this.b.g};k.nf=function(a,b){return B(this.b,"change",a,b)};k.load=function(){this.b.load()};k.Wf=function(a,b){Ka(this.b,"change",a,b)};function Ro(a){a=a||{};this.g=a.font;this.i=a.rotation;this.s=a.rotateWithView;this.a=a.scale;this.v=a.text;this.j=a.textAlign;this.o=a.textBaseline;this.b=void 0!==a.fill?a.fill:new Ti({color:"#333"});this.l=void 0!==a.stroke?a.stroke:null;this.f=void 0!==a.offsetX?a.offsetX:0;this.c=void 0!==a.offsetY?a.offsetY:0}k=Ro.prototype;k.Uj=function(){return this.g};k.hk=function(){return this.f};k.ik=function(){return this.c};k.Pn=function(){return this.b};k.Qn=function(){return this.s};k.Rn=function(){return this.i};
k.Sn=function(){return this.a};k.Tn=function(){return this.l};k.Ja=function(){return this.v};k.vk=function(){return this.j};k.wk=function(){return this.o};k.Zo=function(a){this.g=a};k.ji=function(a){this.f=a};k.ki=function(a){this.c=a};k.Yo=function(a){this.b=a};k.Un=function(a){this.i=a};k.Vn=function(a){this.a=a};k.fp=function(a){this.l=a};k.mi=function(a){this.v=a};k.ni=function(a){this.j=a};k.gp=function(a){this.o=a};function So(a){a=a?a:{};zn.call(this);this.defaultDataProjection=vc("EPSG:4326");var b;a.defaultStyle?b=a.defaultStyle:(b=To)||(Uo=[255,255,255,1],Vo=new Ti({color:Uo}),Wo=[20,2],Xo=Yo="pixels",Zo=[64,64],$o="https://maps.google.com/mapfiles/kml/pushpin/ylw-pushpin.png",ap=.5,bp=new Qo({anchor:Wo,anchorOrigin:"bottom-left",anchorXUnits:Yo,anchorYUnits:Xo,crossOrigin:"anonymous",rotation:0,scale:ap,size:Zo,src:$o}),cp=new Yi({color:Uo,width:1}),dp=new Yi({color:[51,51,51,1],width:2}),ep=new Ro({font:"bold 16px Helvetica",
fill:Vo,stroke:dp,scale:.8}),fp=new Zi({fill:Vo,image:bp,text:ep,stroke:cp,zIndex:0}),b=To=[fp]);this.g=b;this.c=void 0!==a.extractStyles?a.extractStyles:!0;this.l=void 0!==a.writeStyles?a.writeStyles:!0;this.b={};this.i=void 0!==a.showPointNames?a.showPointNames:!0}var To,Uo,Vo,Wo,Yo,Xo,Zo,$o,ap,bp,cp,dp,ep,fp;w(So,zn);
var gp=["http://www.google.com/kml/ext/2.2"],hp=[null,"http://earth.google.com/kml/2.0","http://earth.google.com/kml/2.1","http://earth.google.com/kml/2.2","http://www.opengis.net/kml/2.2"],ip={fraction:"fraction",pixels:"pixels"};
function jp(a,b){var c,d=[0,0],e="start";if(a.a){var f=a.a.kd();f&&2==f.length&&(d[0]=a.a.i*f[0]/2,d[1]=-a.a.i*f[1]/2,e="left")}if(Ca(a.Ja()))c=new Ro({text:b,offsetX:d[0],offsetY:d[1],textAlign:e});else{var f=a.Ja(),g={};for(c in f)g[c]=f[c];c=g;c.mi(b);c.ni(e);c.ji(d[0]);c.ki(d[1])}return new Zi({text:c})}
function kp(a,b,c,d,e){return function(){var f=e,g="";f&&this.Y()&&(f="Point"===this.Y().X());f&&(g=this.get("name"),f=f&&g);if(a)return f?(f=jp(a[0],g),a.concat(f)):a;if(b){var h=lp(b,c,d);return f?(f=jp(h[0],g),h.concat(f)):h}return f?(f=jp(c[0],g),c.concat(f)):c}}function lp(a,b,c){return Array.isArray(a)?a:"string"===typeof a?(!(a in c)&&"#"+a in c&&(a="#"+a),lp(c[a],b,c)):b}
function mp(a){a=Em(a,!1);if(a=/^\s*#?\s*([0-9A-Fa-f]{8})\s*$/.exec(a))return a=a[1],[parseInt(a.substr(6,2),16),parseInt(a.substr(4,2),16),parseInt(a.substr(2,2),16),parseInt(a.substr(0,2),16)/255]}function np(a){a=Em(a,!1);for(var b=[],c=/^\s*([+\-]?\d*\.?\d+(?:e[+\-]?\d+)?)\s*,\s*([+\-]?\d*\.?\d+(?:e[+\-]?\d+)?)(?:\s*,\s*([+\-]?\d*\.?\d+(?:e[+\-]?\d+)?))?\s*/i,d;d=c.exec(a);)b.push(parseFloat(d[1]),parseFloat(d[2]),d[3]?parseFloat(d[3]):0),a=a.substr(d[0].length);return""!==a?void 0:b}
function op(a){var b=Em(a,!1).trim();return a.baseURI?(new URL(b,a.baseURI)).href:b}function pp(a){a=Hn(a);if(void 0!==a)return Math.sqrt(a)}function qp(a,b){return O(null,rp,a,b)}function sp(a,b){var c=O({B:[],wi:[]},tp,a,b);if(c){var d=c.B,c=c.wi,e,f;e=0;for(f=Math.min(d.length,c.length);e<f;++e)d[4*e+3]=c[e];c=new P(null);c.ba("XYZM",d);return c}}function up(a,b){var c=O({},vp,a,b),d=O(null,wp,a,b);if(d){var e=new P(null);e.ba("XYZ",d);e.G(c);return e}}
function xp(a,b){var c=O({},vp,a,b),d=O(null,wp,a,b);if(d){var e=new D(null);e.ba("XYZ",d,[d.length]);e.G(c);return e}}
function yp(a,b){var c=O([],zp,a,b);if(!c)return null;if(0===c.length)return new qn(c);var d,e=!0,f=c[0].X(),g,h,l;h=1;for(l=c.length;h<l;++h)if(g=c[h],g.X()!=f){e=!1;break}if(e)if("Point"==f){d=c[0];e=d.ka;f=d.la();h=1;for(l=c.length;h<l;++h)g=c[h],eb(f,g.la());d=new R(null);d.ba(e,f);Ap(d,c)}else"LineString"==f?(d=new Q(null),fn(d,c),Ap(d,c)):"Polygon"==f?(d=new S(null),hn(d,c),Ap(d,c)):"GeometryCollection"==f?d=new qn(c):la(!1,37);else d=new qn(c);return d}
function Bp(a,b){var c=O({},vp,a,b),d=O(null,wp,a,b);if(d){var e=new C(null);e.ba("XYZ",d);e.G(c);return e}}function Cp(a,b){var c=O({},vp,a,b),d=O([null],Dp,a,b);if(d&&d[0]){var e=new D(null),f=d[0],g=[f.length],h,l;h=1;for(l=d.length;h<l;++h)eb(f,d[h]),g.push(f.length);e.ba("XYZ",f,g);e.G(c);return e}}
function Ep(a,b){var c=O({},Fp,a,b);if(!c)return null;var d="fillStyle"in c?c.fillStyle:Vo,e=c.fill;void 0===e||e||(d=null);var e="imageStyle"in c?c.imageStyle:bp,f="textStyle"in c?c.textStyle:ep,g="strokeStyle"in c?c.strokeStyle:cp,c=c.outline;void 0===c||c||(g=null);return[new Zi({fill:d,image:e,stroke:g,text:f,zIndex:void 0})]}
function Ap(a,b){var c=b.length,d=Array(b.length),e=Array(b.length),f,g,h,l;h=l=!1;for(g=0;g<c;++g)f=b[g],d[g]=f.get("extrude"),e[g]=f.get("altitudeMode"),h=h||void 0!==d[g],l=l||e[g];h&&a.set("extrude",d);l&&a.set("altitudeMode",e)}function Gp(a,b){Sm(Hp,a,b)}
var Ip=N(hp,{value:Lm(T)}),Hp=N(hp,{Data:function(a,b){var c=a.getAttribute("name");if(null!==c){var d=O(void 0,Ip,a,b);d&&(b[b.length-1][c]=d)}},SchemaData:function(a,b){Sm(Jp,a,b)}}),vp=N(hp,{extrude:K(En),altitudeMode:K(T)}),rp=N(hp,{coordinates:Lm(np)}),Dp=N(hp,{innerBoundaryIs:function(a,b){var c=O(void 0,Kp,a,b);c&&b[b.length-1].push(c)},outerBoundaryIs:function(a,b){var c=O(void 0,Lp,a,b);c&&(b[b.length-1][0]=c)}}),tp=N(hp,{when:function(a,b){var c=b[b.length-1].wi,d=Em(a,!1),d=Date.parse(d);
c.push(isNaN(d)?0:d)}},N(gp,{coord:function(a,b){var c=b[b.length-1].B,d=Em(a,!1);(d=/^\s*([+\-]?\d+(?:\.\d*)?(?:e[+\-]?\d*)?)\s+([+\-]?\d+(?:\.\d*)?(?:e[+\-]?\d*)?)\s+([+\-]?\d+(?:\.\d*)?(?:e[+\-]?\d*)?)\s*$/i.exec(d))?c.push(parseFloat(d[1]),parseFloat(d[2]),parseFloat(d[3]),0):c.push(0,0,0,0)}})),wp=N(hp,{coordinates:Lm(np)}),Mp=N(hp,{href:K(op)},N(gp,{x:K(Hn),y:K(Hn),w:K(Hn),h:K(Hn)})),Np=N(hp,{Icon:K(function(a,b){var c=O({},Mp,a,b);return c?c:null}),heading:K(Hn),hotSpot:K(function(a){var b=
a.getAttribute("xunits"),c=a.getAttribute("yunits");return{x:parseFloat(a.getAttribute("x")),Zf:ip[b],y:parseFloat(a.getAttribute("y")),$f:ip[c]}}),scale:K(pp)}),Kp=N(hp,{LinearRing:Lm(qp)}),Op=N(hp,{color:K(mp),scale:K(pp)}),Pp=N(hp,{color:K(mp),width:K(Hn)}),zp=N(hp,{LineString:Km(up),LinearRing:Km(xp),MultiGeometry:Km(yp),Point:Km(Bp),Polygon:Km(Cp)}),Qp=N(gp,{Track:Km(sp)}),Sp=N(hp,{ExtendedData:Gp,Link:function(a,b){Sm(Rp,a,b)},address:K(T),description:K(T),name:K(T),open:K(En),phoneNumber:K(T),
visibility:K(En)}),Rp=N(hp,{href:K(op)}),Lp=N(hp,{LinearRing:Lm(qp)}),Tp=N(hp,{Style:K(Ep),key:K(T),styleUrl:K(op)}),Vp=N(hp,{ExtendedData:Gp,MultiGeometry:K(yp,"geometry"),LineString:K(up,"geometry"),LinearRing:K(xp,"geometry"),Point:K(Bp,"geometry"),Polygon:K(Cp,"geometry"),Style:K(Ep),StyleMap:function(a,b){var c=O(void 0,Up,a,b);if(c){var d=b[b.length-1];Array.isArray(c)?d.Style=c:"string"===typeof c?d.styleUrl=c:la(!1,38)}},address:K(T),description:K(T),name:K(T),open:K(En),phoneNumber:K(T),
styleUrl:K(op),visibility:K(En)},N(gp,{MultiTrack:K(function(a,b){var c=O([],Qp,a,b);if(c){var d=new Q(null);fn(d,c);return d}},"geometry"),Track:K(sp,"geometry")})),Wp=N(hp,{color:K(mp),fill:K(En),outline:K(En)}),Jp=N(hp,{SimpleData:function(a,b){var c=a.getAttribute("name");if(null!==c){var d=T(a);b[b.length-1][c]=d}}}),Fp=N(hp,{IconStyle:function(a,b){var c=O({},Np,a,b);if(c){var d=b[b.length-1],e="Icon"in c?c.Icon:{},f;f=(f=e.href)?f:$o;var g,h,l,m=c.hotSpot;m?(g=[m.x,m.y],h=m.Zf,l=m.$f):f===
$o?(g=Wo,h=Yo,l=Xo):/^http:\/\/maps\.(?:google|gstatic)\.com\//.test(f)&&(g=[.5,0],l=h="fraction");var n,m=e.x,p=e.y;void 0!==m&&void 0!==p&&(n=[m,p]);var q,m=e.w,e=e.h;void 0!==m&&void 0!==e&&(q=[m,e]);var t,e=c.heading;void 0!==e&&(t=sa(e));c=c.scale;f==$o&&(q=Zo,void 0===c&&(c=ap));g=new Qo({anchor:g,anchorOrigin:"bottom-left",anchorXUnits:h,anchorYUnits:l,crossOrigin:"anonymous",offset:n,offsetOrigin:"bottom-left",rotation:t,scale:c,size:q,src:f});d.imageStyle=g}},LabelStyle:function(a,b){var c=
O({},Op,a,b);c&&(b[b.length-1].textStyle=new Ro({fill:new Ti({color:"color"in c?c.color:Uo}),scale:c.scale}))},LineStyle:function(a,b){var c=O({},Pp,a,b);c&&(b[b.length-1].strokeStyle=new Yi({color:"color"in c?c.color:Uo,width:"width"in c?c.width:1}))},PolyStyle:function(a,b){var c=O({},Wp,a,b);if(c){var d=b[b.length-1];d.fillStyle=new Ti({color:"color"in c?c.color:Uo});var e=c.fill;void 0!==e&&(d.fill=e);c=c.outline;void 0!==c&&(d.outline=c)}}}),Up=N(hp,{Pair:function(a,b){var c=O({},Tp,a,b);if(c){var d=
c.key;d&&"normal"==d&&((d=c.styleUrl)&&(b[b.length-1]=d),(c=c.Style)&&(b[b.length-1]=c))}}});k=So.prototype;k.Ff=function(a,b){var c=N(hp,{Document:Jm(this.Ff,this),Folder:Jm(this.Ff,this),Placemark:Km(this.Nf,this),Style:this.Io.bind(this),StyleMap:this.Ho.bind(this)});if(c=O([],c,a,b,this))return c};
k.Nf=function(a,b){var c=O({geometry:null},Vp,a,b);if(c){var d=new Am,e=a.getAttribute("id");null!==e&&d.Xb(e);var e=b[0],f=c.geometry;f&&$m(f,!1,e);d.Xa(f);delete c.geometry;this.c&&d.rf(kp(c.Style,c.styleUrl,this.g,this.b,this.i));delete c.Style;d.G(c);return d}};k.Io=function(a,b){var c=a.getAttribute("id");if(null!==c){var d=Ep(a,b);d&&(c=a.baseURI?(new URL("#"+c,a.baseURI)).href:"#"+c,this.b[c]=d)}};
k.Ho=function(a,b){var c=a.getAttribute("id");if(null!==c){var d=O(void 0,Up,a,b);d&&(c=a.baseURI?(new URL("#"+c,a.baseURI)).href:"#"+c,this.b[c]=d)}};k.Mh=function(a,b){if(!bb(hp,a.namespaceURI))return null;var c=this.Nf(a,[Ym(this,a,b)]);return c?c:null};
k.nc=function(a,b){if(!bb(hp,a.namespaceURI))return[];var c;c=a.localName;if("Document"==c||"Folder"==c)return(c=this.Ff(a,[Ym(this,a,b)]))?c:[];if("Placemark"==c)return(c=this.Nf(a,[Ym(this,a,b)]))?[c]:[];if("kml"==c){c=[];var d;for(d=a.firstElementChild;d;d=d.nextElementSibling){var e=this.nc(d,b);e&&eb(c,e)}return c}return[]};k.Co=function(a){if(Gm(a))return Xp(this,a);if(Hm(a))return Yp(this,a);if("string"===typeof a)return a=Im(a),Xp(this,a)};
function Xp(a,b){var c;for(c=b.firstChild;c;c=c.nextSibling)if(c.nodeType==Node.ELEMENT_NODE){var d=Yp(a,c);if(d)return d}}function Yp(a,b){var c;for(c=b.firstElementChild;c;c=c.nextElementSibling)if(bb(hp,c.namespaceURI)&&"name"==c.localName)return T(c);for(c=b.firstElementChild;c;c=c.nextElementSibling){var d=c.localName;if(bb(hp,c.namespaceURI)&&("Document"==d||"Folder"==d||"Placemark"==d||"kml"==d)&&(d=Yp(a,c)))return d}}
k.Do=function(a){var b=[];Gm(a)?eb(b,Zp(this,a)):Hm(a)?eb(b,$p(this,a)):"string"===typeof a&&(a=Im(a),eb(b,Zp(this,a)));return b};function Zp(a,b){var c,d=[];for(c=b.firstChild;c;c=c.nextSibling)c.nodeType==Node.ELEMENT_NODE&&eb(d,$p(a,c));return d}
function $p(a,b){var c,d=[];for(c=b.firstElementChild;c;c=c.nextElementSibling)if(bb(hp,c.namespaceURI)&&"NetworkLink"==c.localName){var e=O({},Sp,c,[]);d.push(e)}for(c=b.firstElementChild;c;c=c.nextElementSibling)e=c.localName,!bb(hp,c.namespaceURI)||"Document"!=e&&"Folder"!=e&&"kml"!=e||eb(d,$p(a,c));return d}function aq(a,b){var c=Ae(b),c=[255*(4==c.length?c[3]:1),c[2],c[1],c[0]],d;for(d=0;4>d;++d){var e=parseInt(c[d],10).toString(16);c[d]=1==e.length?"0"+e:e}Mn(a,c.join(""))}
function bq(a,b,c){a={node:a};var d=b.X(),e,f;"GeometryCollection"==d?(e=b.df(),f=cq):"MultiPoint"==d?(e=b.he(),f=dq):"MultiLineString"==d?(e=b.ld(),f=eq):"MultiPolygon"==d?(e=b.Wd(),f=fq):la(!1,39);Tm(a,gq,f,e,c)}function hq(a,b,c){Tm({node:a},iq,jq,[b],c)}
function kq(a,b,c){var d={node:a};b.Oa()&&a.setAttribute("id",b.Oa());a=b.N();var e=b.hc();e&&(e=e.call(b,0))&&(e=Array.isArray(e)?e[0]:e,this.l&&(a.Style=e),(e=e.Ja())&&(a.name=e.Ja()));e=lq[c[c.length-1].node.namespaceURI];a=Rm(a,e);Tm(d,mq,Qm,a,c,e);a=c[0];(b=b.Y())&&(b=$m(b,!0,a));Tm(d,mq,cq,[b],c)}function nq(a,b,c){var d=b.la();a={node:a};a.layout=b.ka;a.stride=b.ua();Tm(a,oq,pq,[d],c)}function qq(a,b,c){b=b.Vd();var d=b.shift();a={node:a};Tm(a,rq,sq,b,c);Tm(a,rq,tq,[d],c)}
function uq(a,b){Nn(a,Math.round(b*b*1E6)/1E6)}
var vq=N(hp,["Document","Placemark"]),yq=N(hp,{Document:L(function(a,b,c){Tm({node:a},wq,xq,b,c,void 0,this)}),Placemark:L(kq)}),wq=N(hp,{Placemark:L(kq)}),zq={Point:"Point",LineString:"LineString",LinearRing:"LinearRing",Polygon:"Polygon",MultiPoint:"MultiGeometry",MultiLineString:"MultiGeometry",MultiPolygon:"MultiGeometry",GeometryCollection:"MultiGeometry"},Aq=N(hp,["href"],N(gp,["x","y","w","h"])),Bq=N(hp,{href:L(Mn)},N(gp,{x:L(Nn),y:L(Nn),w:L(Nn),h:L(Nn)})),Cq=N(hp,["scale","heading","Icon",
"hotSpot"]),Eq=N(hp,{Icon:L(function(a,b,c){a={node:a};var d=Aq[c[c.length-1].node.namespaceURI],e=Rm(b,d);Tm(a,Bq,Qm,e,c,d);d=Aq[gp[0]];e=Rm(b,d);Tm(a,Bq,Dq,e,c,d)}),heading:L(Nn),hotSpot:L(function(a,b){a.setAttribute("x",b.x);a.setAttribute("y",b.y);a.setAttribute("xunits",b.Zf);a.setAttribute("yunits",b.$f)}),scale:L(uq)}),Fq=N(hp,["color","scale"]),Gq=N(hp,{color:L(aq),scale:L(uq)}),Hq=N(hp,["color","width"]),Iq=N(hp,{color:L(aq),width:L(Nn)}),iq=N(hp,{LinearRing:L(nq)}),gq=N(hp,{LineString:L(nq),
Point:L(nq),Polygon:L(qq),GeometryCollection:L(bq)}),lq=N(hp,"name open visibility address phoneNumber description styleUrl Style".split(" ")),mq=N(hp,{MultiGeometry:L(bq),LineString:L(nq),LinearRing:L(nq),Point:L(nq),Polygon:L(qq),Style:L(function(a,b,c){a={node:a};var d={},e=b.c,f=b.f,g=b.a;b=b.Ja();g instanceof Qo&&(d.IconStyle=g);b&&(d.LabelStyle=b);f&&(d.LineStyle=f);e&&(d.PolyStyle=e);b=Jq[c[c.length-1].node.namespaceURI];d=Rm(d,b);Tm(a,Kq,Qm,d,c,b)}),address:L(Mn),description:L(Mn),name:L(Mn),
open:L(Ln),phoneNumber:L(Mn),styleUrl:L(Mn),visibility:L(Ln)}),oq=N(hp,{coordinates:L(function(a,b,c){c=c[c.length-1];var d=c.layout;c=c.stride;var e;"XY"==d||"XYM"==d?e=2:"XYZ"==d||"XYZM"==d?e=3:la(!1,34);var f,g=b.length,h="";if(0<g){h+=b[0];for(d=1;d<e;++d)h+=","+b[d];for(f=c;f<g;f+=c)for(h+=" "+b[f],d=1;d<e;++d)h+=","+b[f+d]}Mn(a,h)})}),rq=N(hp,{outerBoundaryIs:L(hq),innerBoundaryIs:L(hq)}),Lq=N(hp,{color:L(aq)}),Jq=N(hp,["IconStyle","LabelStyle","LineStyle","PolyStyle"]),Kq=N(hp,{IconStyle:L(function(a,
b,c){a={node:a};var d={},e=b.Jb(),f=b.kd(),g={href:b.b.o};if(e){g.w=e[0];g.h=e[1];var h=b.bc(),l=b.Ka();l&&f&&0!==l[0]&&l[1]!==e[1]&&(g.x=l[0],g.y=f[1]-(l[1]+e[1]));h&&0!==h[0]&&h[1]!==e[1]&&(d.hotSpot={x:h[0],Zf:"pixels",y:e[1]-h[1],$f:"pixels"})}d.Icon=g;e=b.i;1!==e&&(d.scale=e);b=b.o;0!==b&&(d.heading=b);b=Cq[c[c.length-1].node.namespaceURI];d=Rm(d,b);Tm(a,Eq,Qm,d,c,b)}),LabelStyle:L(function(a,b,c){a={node:a};var d={},e=b.b;e&&(d.color=e.b);(b=b.a)&&1!==b&&(d.scale=b);b=Fq[c[c.length-1].node.namespaceURI];
d=Rm(d,b);Tm(a,Gq,Qm,d,c,b)}),LineStyle:L(function(a,b,c){a={node:a};var d=Hq[c[c.length-1].node.namespaceURI];b=Rm({color:b.b,width:b.a},d);Tm(a,Iq,Qm,b,c,d)}),PolyStyle:L(function(a,b,c){Tm({node:a},Lq,Mq,[b.b],c)})});function Dq(a,b,c){return Dm(gp[0],"gx:"+c)}function xq(a,b){return Dm(b[b.length-1].node.namespaceURI,"Placemark")}function cq(a,b){if(a)return Dm(b[b.length-1].node.namespaceURI,zq[a.X()])}
var Mq=Om("color"),pq=Om("coordinates"),sq=Om("innerBoundaryIs"),dq=Om("Point"),eq=Om("LineString"),jq=Om("LinearRing"),fq=Om("Polygon"),tq=Om("outerBoundaryIs");
So.prototype.a=function(a,b){b=Zm(this,b);var c=Dm(hp[4],"kml");c.setAttributeNS("http://www.w3.org/2000/xmlns/","xmlns:gx",gp[0]);c.setAttributeNS("http://www.w3.org/2000/xmlns/","xmlns:xsi","http://www.w3.org/2001/XMLSchema-instance");c.setAttributeNS("http://www.w3.org/2001/XMLSchema-instance","xsi:schemaLocation","http://www.opengis.net/kml/2.2 https://developers.google.com/kml/schema/kml22gx.xsd");var d={node:c},e={};1<a.length?e.Document=a:1==a.length&&(e.Placemark=a[0]);var f=vq[c.namespaceURI],
e=Rm(e,f);Tm(d,yq,Qm,e,[b],f,this);return c};var Nq,Oq,Pq,Qq;
(function(){var a={},b={ja:a};(function(c){if("object"===typeof a&&"undefined"!==typeof b)b.ja=c();else{var d;"undefined"!==typeof window?d=window:"undefined"!==typeof global?d=global:"undefined"!==typeof self?d=self:d=this;d.Np=c()}})(function(){return function d(a,b,g){function h(m,p){if(!b[m]){if(!a[m]){var q="function"==typeof require&&require;if(!p&&q)return q(m,!0);if(l)return l(m,!0);q=Error("Cannot find module '"+m+"'");throw q.code="MODULE_NOT_FOUND",q;}q=b[m]={ja:{}};a[m][0].call(q.ja,function(b){var d=
a[m][1][b];return h(d?d:b)},q,q.ja,d,a,b,g)}return b[m].ja}for(var l="function"==typeof require&&require,m=0;m<g.length;m++)h(g[m]);return h}({1:[function(a,b,f){f.read=function(a,b,d,e,f){var p;p=8*f-e-1;var q=(1<<p)-1,t=q>>1,v=-7;f=d?f-1:0;var u=d?-1:1,y=a[b+f];f+=u;d=y&(1<<-v)-1;y>>=-v;for(v+=p;0<v;d=256*d+a[b+f],f+=u,v-=8);p=d&(1<<-v)-1;d>>=-v;for(v+=e;0<v;p=256*p+a[b+f],f+=u,v-=8);if(0===d)d=1-t;else{if(d===q)return p?NaN:Infinity*(y?-1:1);p+=Math.pow(2,e);d-=t}return(y?-1:1)*p*Math.pow(2,d-
e)};f.write=function(a,b,d,e,f,p){var q,t=8*p-f-1,v=(1<<t)-1,u=v>>1,y=23===f?Math.pow(2,-24)-Math.pow(2,-77):0;p=e?0:p-1;var E=e?1:-1,z=0>b||0===b&&0>1/b?1:0;b=Math.abs(b);isNaN(b)||Infinity===b?(b=isNaN(b)?1:0,e=v):(e=Math.floor(Math.log(b)/Math.LN2),1>b*(q=Math.pow(2,-e))&&(e--,q*=2),b=1<=e+u?b+y/q:b+y*Math.pow(2,1-u),2<=b*q&&(e++,q/=2),e+u>=v?(b=0,e=v):1<=e+u?(b=(b*q-1)*Math.pow(2,f),e+=u):(b=b*Math.pow(2,u-1)*Math.pow(2,f),e=0));for(;8<=f;a[d+p]=b&255,p+=E,b/=256,f-=8);e=e<<f|b;for(t+=f;0<t;a[d+
p]=e&255,p+=E,e/=256,t-=8);a[d+p-E]|=128*z}},{}],2:[function(a,b){function f(a){var b;a&&a.length&&(b=a,a=b.length);a=new Uint8Array(a||0);b&&a.set(b);a.Xh=l.Xh;a.Yf=l.Yf;a.Ph=l.Ph;a.Bi=l.Bi;a.Mf=l.Mf;a.Ai=l.Ai;a.Gf=l.Gf;a.xi=l.xi;a.toString=l.toString;a.write=l.write;a.slice=l.slice;a.rg=l.rg;a.jj=!0;return a}function g(a){for(var b=a.length,d=[],e=0,f,g;e<b;e++){f=a.charCodeAt(e);if(55295<f&&57344>f)if(g)if(56320>f){d.push(239,191,189);g=f;continue}else f=g-55296<<10|f-56320|65536,g=null;else{56319<
f||e+1===b?d.push(239,191,189):g=f;continue}else g&&(d.push(239,191,189),g=null);128>f?d.push(f):2048>f?d.push(f>>6|192,f&63|128):65536>f?d.push(f>>12|224,f>>6&63|128,f&63|128):d.push(f>>18|240,f>>12&63|128,f>>6&63|128,f&63|128)}return d}b.ja=f;var h=a("ieee754"),l,m,n;l={Xh:function(a){return(this[a]|this[a+1]<<8|this[a+2]<<16)+16777216*this[a+3]},Yf:function(a,b){this[b]=a;this[b+1]=a>>>8;this[b+2]=a>>>16;this[b+3]=a>>>24},Ph:function(a){return(this[a]|this[a+1]<<8|this[a+2]<<16)+(this[a+3]<<24)},
Mf:function(a){return h.read(this,a,!0,23,4)},Gf:function(a){return h.read(this,a,!0,52,8)},Ai:function(a,b){return h.write(this,a,b,!0,23,4)},xi:function(a,b){return h.write(this,a,b,!0,52,8)},toString:function(a,b,d){var e=a="";d=Math.min(this.length,d||this.length);for(b=b||0;b<d;b++){var f=this[b];127>=f?(a+=decodeURIComponent(e)+String.fromCharCode(f),e=""):e+="%"+f.toString(16)}return a+=decodeURIComponent(e)},write:function(a,b){for(var d=a===m?n:g(a),e=0;e<d.length;e++)this[b+e]=d[e]},slice:function(a,
b){return this.subarray(a,b)},rg:function(a,b){b=b||0;for(var d=0;d<this.length;d++)a[b+d]=this[d]}};l.Bi=l.Yf;f.byteLength=function(a){m=a;n=g(a);return n.length};f.isBuffer=function(a){return!(!a||!a.jj)}},{ieee754:1}],3:[function(a,b){(function(f){function g(a){this.Gb=l.isBuffer(a)?a:new l(a||0);this.da=0;this.length=this.Gb.length}function h(a,b){var d=b.Gb,e;e=d[b.da++];a+=268435456*(e&127);if(128>e)return a;e=d[b.da++];a+=34359738368*(e&127);if(128>e)return a;e=d[b.da++];a+=4398046511104*(e&
127);if(128>e)return a;e=d[b.da++];a+=562949953421312*(e&127);if(128>e)return a;e=d[b.da++];a+=72057594037927936*(e&127);if(128>e)return a;e=d[b.da++];if(128>e)return a+0x7fffffffffffffff*(e&127);throw Error("Expected varint not more than 10 bytes");}b.ja=g;var l=f.xp||a("./buffer");g.f=0;g.g=1;g.b=2;g.a=5;var m=Math.pow(2,63);g.prototype={Kf:function(a,b,d){for(d=d||this.length;this.da<d;){var e=this.Da(),f=this.da;a(e>>3,b,this);this.da===f&&this.lp(e)}return b},yo:function(){var a=this.Gb.Mf(this.da);
this.da+=4;return a},uo:function(){var a=this.Gb.Gf(this.da);this.da+=8;return a},Da:function(){var a=this.Gb,b,d;d=a[this.da++];b=d&127;if(128>d)return b;d=a[this.da++];b|=(d&127)<<7;if(128>d)return b;d=a[this.da++];b|=(d&127)<<14;if(128>d)return b;d=a[this.da++];b|=(d&127)<<21;return 128>d?b:h(b,this)},Jo:function(){var a=this.da,b=this.Da();if(b<m)return b;for(var d=this.da-2;255===this.Gb[d];)d--;d<a&&(d=a);for(var e=b=0;e<d-a+1;e++)var f=~this.Gb[a+e]&127,b=b+(4>e?f<<7*e:f*Math.pow(2,7*e));return-b-
1},wd:function(){var a=this.Da();return 1===a%2?(a+1)/-2:a/2},so:function(){return!!this.Da()},Pf:function(){var a=this.Da()+this.da,b=this.Gb.toString("utf8",this.da,a);this.da=a;return b},lp:function(a){a&=7;if(a===g.f)for(;127<this.Gb[this.da++];);else if(a===g.b)this.da=this.Da()+this.da;else if(a===g.a)this.da+=4;else if(a===g.g)this.da+=8;else throw Error("Unimplemented type: "+a);}}}).call(this,"undefined"!==typeof global?global:"undefined"!==typeof self?self:"undefined"!==typeof window?window:
{})},{"./buffer":2}]},{},[3])(3)});Nq=b.ja})();(function(){var a={},b={ja:a};(function(c){if("object"===typeof a&&"undefined"!==typeof b)b.ja=c();else{var d;"undefined"!==typeof window?d=window:"undefined"!==typeof global?d=global:"undefined"!==typeof self?d=self:d=this;d.Qp=c()}})(function(){return function d(a,b,g){function h(m,p){if(!b[m]){if(!a[m]){var q="function"==typeof require&&require;if(!p&&q)return q(m,!0);if(l)return l(m,!0);q=Error("Cannot find module '"+m+"'");throw q.code="MODULE_NOT_FOUND",q;}q=b[m]={ja:{}};a[m][0].call(q.ja,function(b){var d=
a[m][1][b];return h(d?d:b)},q,q.ja,d,a,b,g)}return b[m].ja}for(var l="function"==typeof require&&require,m=0;m<g.length;m++)h(g[m]);return h}({1:[function(a,b){function f(a,b){this.x=a;this.y=b}b.ja=f;f.prototype={clone:function(){return new f(this.x,this.y)},add:function(a){return this.clone().bj(a)},rotate:function(a){return this.clone().mj(a)},round:function(){return this.clone().nj()},angle:function(){return Math.atan2(this.y,this.x)},bj:function(a){this.x+=a.x;this.y+=a.y;return this},mj:function(a){var b=
Math.cos(a);a=Math.sin(a);var d=a*this.x+b*this.y;this.x=b*this.x-a*this.y;this.y=d;return this},nj:function(){this.x=Math.round(this.x);this.y=Math.round(this.y);return this}};f.b=function(a){return a instanceof f?a:Array.isArray(a)?new f(a[0],a[1]):a}},{}],2:[function(a,b){b.ja.aj=a("./lib/vectortile.js");b.ja.Kp=a("./lib/vectortilefeature.js");b.ja.Lp=a("./lib/vectortilelayer.js")},{"./lib/vectortile.js":3,"./lib/vectortilefeature.js":4,"./lib/vectortilelayer.js":5}],3:[function(a,b){function f(a,
b,d){3===a&&(a=new g(d,d.Da()+d.da),a.length&&(b[a.name]=a))}var g=a("./vectortilelayer");b.ja=function(a,b){this.layers=a.Kf(f,{},b)}},{"./vectortilelayer":5}],4:[function(a,b){function f(a,b,d,e,f){this.properties={};this.extent=d;this.type=0;this.qc=a;this.Oe=-1;this.Hd=e;this.Jd=f;a.Kf(g,this,b)}function g(a,b,d){if(1==a)b.id=d.Da();else if(2==a)for(a=d.Da()+d.da;d.da<a;){var e=b.Hd[d.Da()],f=b.Jd[d.Da()];b.properties[e]=f}else 3==a?b.type=d.Da():4==a&&(b.Oe=d.da)}var h=a("point-geometry");b.ja=
f;f.b=["Unknown","Point","LineString","Polygon"];f.prototype.Sg=function(){var a=this.qc;a.da=this.Oe;for(var b=a.Da()+a.da,d=1,e=0,f=0,g=0,v=[],u;a.da<b;)if(e||(e=a.Da(),d=e&7,e>>=3),e--,1===d||2===d)f+=a.wd(),g+=a.wd(),1===d&&(u&&v.push(u),u=[]),u.push(new h(f,g));else if(7===d)u&&u.push(u[0].clone());else throw Error("unknown command "+d);u&&v.push(u);return v};f.prototype.bbox=function(){var a=this.qc;a.da=this.Oe;for(var b=a.Da()+a.da,d=1,e=0,f=0,g=0,h=Infinity,u=-Infinity,y=Infinity,E=-Infinity;a.da<
b;)if(e||(e=a.Da(),d=e&7,e>>=3),e--,1===d||2===d)f+=a.wd(),g+=a.wd(),f<h&&(h=f),f>u&&(u=f),g<y&&(y=g),g>E&&(E=g);else if(7!==d)throw Error("unknown command "+d);return[h,y,u,E]}},{"point-geometry":1}],5:[function(a,b){function f(a,b){this.version=1;this.name=null;this.extent=4096;this.length=0;this.qc=a;this.Hd=[];this.Jd=[];this.Gd=[];a.Kf(g,this,b);this.length=this.Gd.length}function g(a,b,d){15===a?b.version=d.Da():1===a?b.name=d.Pf():5===a?b.extent=d.Da():2===a?b.Gd.push(d.da):3===a?b.Hd.push(d.Pf()):
4===a&&b.Jd.push(h(d))}function h(a){for(var b=null,d=a.Da()+a.da;a.da<d;)b=a.Da()>>3,b=1===b?a.Pf():2===b?a.yo():3===b?a.uo():4===b?a.Jo():5===b?a.Da():6===b?a.wd():7===b?a.so():null;return b}var l=a("./vectortilefeature.js");b.ja=f;f.prototype.feature=function(a){if(0>a||a>=this.Gd.length)throw Error("feature index out of bounds");this.qc.da=this.Gd[a];a=this.qc.Da()+this.qc.da;return new l(this.qc,a,this.extent,this.Hd,this.Jd)}},{"./vectortilefeature.js":4}]},{},[2])(2)});Oq=b.ja})();function Rq(a,b,c,d){this.g=a;this.b=b;this.c=c;this.f=d}k=Rq.prototype;k.get=function(a){return this.f[a]};k.Hb=function(){return this.c};k.C=function(){this.a||(this.a="Point"===this.g?Rb(this.b):Sb(this.b,0,this.b.length,2));return this.a};k.Qb=function(){return this.b};k.la=Rq.prototype.Qb;k.Y=function(){return this};k.Im=function(){return this.f};k.md=Rq.prototype.Y;k.ua=function(){return 2};k.hc=ha;k.X=function(){return this.g};function Sq(a){this.defaultDataProjection=null;a=a?a:{};this.defaultDataProjection=new sc({code:"",units:"tile-pixels"});this.b=a.featureClass?a.featureClass:Rq;this.g=a.geometryName?a.geometryName:"geometry";this.a=a.layerName?a.layerName:"layer";this.f=a.layers?a.layers:null}w(Sq,Xm);Sq.prototype.X=function(){return"arraybuffer"};
Sq.prototype.Fa=function(a,b){var c=this.f,d=new Nq(a),d=new Oq.aj(d),e=[],f=this.b,g,h,l;for(l in d.layers)if(!c||-1!=c.indexOf(l)){g=d.layers[l];for(var m=0,n=g.length;m<n;++m){if(f===Rq){var p=g.feature(m);h=l;var q=p.Sg(),t=[],v=[];Tq(q,v,t);var u=p.type,y=void 0;1===u?y=1===q.length?"Point":"MultiPoint":2===u?y=1===q.length?"LineString":"MultiLineString":3===u&&(y="Polygon");p=p.properties;p[this.a]=h;h=new this.b(y,v,t,p)}else{q=g.feature(m);p=l;y=b;h=new this.b;t=q.id;v=q.properties;v[this.a]=
p;p=q.type;if(0===p)p=null;else{var q=q.Sg(),u=[],E=[];Tq(q,E,u);var z=void 0;1===p?z=1===q.length?new C(null):new R(null):2===p?1===q.length?z=new P(null):z=new Q(null):3===p&&(z=new D(null));z.ba("XY",E,u);p=z}(y=$m(p,!1,Zm(this,y)))&&(v[this.g]=y);h.Xb(t);h.G(v);h.Cc(this.g)}e.push(h)}}return e};Sq.prototype.Ra=function(){return this.defaultDataProjection};Sq.prototype.c=function(a){this.f=a};
function Tq(a,b,c){for(var d=0,e=0,f=a.length;e<f;++e){var g=a[e],h,l;h=0;for(l=g.length;h<l;++h){var m=g[h];b.push(m.x,m.y)}d+=2*h;c.push(d)}};function Uq(a){this.Kb=a};function Vq(a){this.Kb=a}w(Vq,Uq);function Wq(a,b,c){this.Kb=a;this.b=b;this.a=c}w(Wq,Vq);function Xq(a,b){Wq.call(this,"And",a,b)}w(Xq,Wq);function Yq(a,b,c){this.Kb="BBOX";this.geometryName=a;this.extent=b;this.srsName=c}w(Yq,Uq);function Zq(a,b){this.Kb=a;this.b=b}w(Zq,Uq);function $q(a,b,c,d){Zq.call(this,a,b);this.g=c;this.a=d}w($q,Zq);function ar(a,b,c){$q.call(this,"PropertyIsEqualTo",a,b,c)}w(ar,$q);function br(a,b){$q.call(this,"PropertyIsGreaterThan",a,b)}w(br,$q);function cr(a,b){$q.call(this,"PropertyIsGreaterThanOrEqualTo",a,b)}w(cr,$q);function dr(a,b,c,d){this.Kb=a;this.geometryName=b||"the_geom";this.geometry=c;this.srsName=d}w(dr,Uq);function er(a,b,c){dr.call(this,"Intersects",a,b,c)}w(er,dr);function fr(a,b,c){Zq.call(this,"PropertyIsBetween",a);this.a=b;this.g=c}w(fr,Zq);function gr(a,b,c,d,e,f){Zq.call(this,"PropertyIsLike",a);this.f=b;this.i=void 0!==c?c:"*";this.c=void 0!==d?d:".";this.g=void 0!==e?e:"!";this.a=f}w(gr,Zq);function hr(a){Zq.call(this,"PropertyIsNull",a)}w(hr,Zq);function ir(a,b){$q.call(this,"PropertyIsLessThan",a,b)}w(ir,$q);function jr(a,b){$q.call(this,"PropertyIsLessThanOrEqualTo",a,b)}w(jr,$q);function kr(a){this.Kb="Not";this.condition=a}w(kr,Vq);function lr(a,b,c){$q.call(this,"PropertyIsNotEqualTo",a,b,c)}w(lr,$q);function mr(a,b){Wq.call(this,"Or",a,b)}w(mr,Wq);function nr(a,b,c){dr.call(this,"Within",a,b,c)}w(nr,dr);function or(a,b){return new Xq(a,b)}function pr(a,b,c){return new Yq(a,b,c)};function qr(){zn.call(this);this.defaultDataProjection=vc("EPSG:4326")}w(qr,zn);function rr(a,b){b[b.length-1].Bd[a.getAttribute("k")]=a.getAttribute("v")}
var sr=[null],tr=N(sr,{nd:function(a,b){b[b.length-1].Pc.push(a.getAttribute("ref"))},tag:rr}),vr=N(sr,{node:function(a,b){var c=b[0],d=b[b.length-1],e=a.getAttribute("id"),f=[parseFloat(a.getAttribute("lon")),parseFloat(a.getAttribute("lat"))];d.Wg[e]=f;var g=O({Bd:{}},ur,a,b);Ca(g.Bd)||(f=new C(f),$m(f,!1,c),c=new Am(f),c.Xb(e),c.G(g.Bd),d.features.push(c))},way:function(a,b){for(var c=b[0],d=a.getAttribute("id"),e=O({Pc:[],Bd:{}},tr,a,b),f=b[b.length-1],g=[],h=0,l=e.Pc.length;h<l;h++)eb(g,f.Wg[e.Pc[h]]);
e.Pc[0]==e.Pc[e.Pc.length-1]?(h=new D(null),h.ba("XY",g,[g.length])):(h=new P(null),h.ba("XY",g));$m(h,!1,c);c=new Am(h);c.Xb(d);c.G(e.Bd);f.features.push(c)}}),ur=N(sr,{tag:rr});qr.prototype.nc=function(a,b){var c=Ym(this,a,b);return"osm"==a.localName&&(c=O({Wg:{},features:[]},vr,a,[c]),c.features)?c.features:[]};function wr(a){return a.getAttributeNS("http://www.w3.org/1999/xlink","href")};function xr(){}xr.prototype.read=function(a){return Gm(a)?this.a(a):Hm(a)?this.b(a):"string"===typeof a?(a=Im(a),this.a(a)):null};function yr(){}w(yr,xr);yr.prototype.a=function(a){for(a=a.firstChild;a;a=a.nextSibling)if(a.nodeType==Node.ELEMENT_NODE)return this.b(a);return null};yr.prototype.b=function(a){return(a=O({},zr,a,[]))?a:null};
var Ar=[null,"http://www.opengis.net/ows/1.1"],zr=N(Ar,{ServiceIdentification:K(function(a,b){return O({},Br,a,b)}),ServiceProvider:K(function(a,b){return O({},Cr,a,b)}),OperationsMetadata:K(function(a,b){return O({},Dr,a,b)})}),Er=N(Ar,{DeliveryPoint:K(T),City:K(T),AdministrativeArea:K(T),PostalCode:K(T),Country:K(T),ElectronicMailAddress:K(T)}),Fr=N(Ar,{Value:Mm(function(a){return T(a)})}),Gr=N(Ar,{AllowedValues:K(function(a,b){return O({},Fr,a,b)})}),Ir=N(Ar,{Phone:K(function(a,b){return O({},
Hr,a,b)}),Address:K(function(a,b){return O({},Er,a,b)})}),Kr=N(Ar,{HTTP:K(function(a,b){return O({},Jr,a,b)})}),Jr=N(Ar,{Get:Mm(function(a,b){var c=wr(a);return c?O({href:c},Lr,a,b):void 0}),Post:void 0}),Mr=N(Ar,{DCP:K(function(a,b){return O({},Kr,a,b)})}),Dr=N(Ar,{Operation:function(a,b){var c=a.getAttribute("name"),d=O({},Mr,a,b);d&&(b[b.length-1][c]=d)}}),Hr=N(Ar,{Voice:K(T),Facsimile:K(T)}),Lr=N(Ar,{Constraint:Mm(function(a,b){var c=a.getAttribute("name");return c?O({name:c},Gr,a,b):void 0})}),
Nr=N(Ar,{IndividualName:K(T),PositionName:K(T),ContactInfo:K(function(a,b){return O({},Ir,a,b)})}),Br=N(Ar,{Title:K(T),ServiceTypeVersion:K(T),ServiceType:K(T)}),Cr=N(Ar,{ProviderName:K(T),ProviderSite:K(wr),ServiceContact:K(function(a,b){return O({},Nr,a,b)})});function Or(a,b,c,d){var e;void 0!==d?e=d:e=[];for(var f=d=0;f<b;){var g=a[f++];e[d++]=a[f++];e[d++]=g;for(g=2;g<c;++g)e[d++]=a[f++]}e.length=d};function Pr(a){a=a?a:{};this.defaultDataProjection=null;this.defaultDataProjection=vc("EPSG:4326");this.b=a.factor?a.factor:1E5;this.a=a.geometryLayout?a.geometryLayout:"XY"}w(Pr,Ho);function Qr(a,b,c){var d,e=Array(b);for(d=0;d<b;++d)e[d]=0;var f,g;f=0;for(g=a.length;f<g;)for(d=0;d<b;++d,++f){var h=a[f],l=h-e[d];e[d]=h;a[f]=l}return Rr(a,c?c:1E5)}
function Sr(a,b,c){var d,e=Array(b);for(d=0;d<b;++d)e[d]=0;a=Tr(a,c?c:1E5);var f;c=0;for(f=a.length;c<f;)for(d=0;d<b;++d,++c)e[d]+=a[c],a[c]=e[d];return a}function Rr(a,b){var c=b?b:1E5,d,e;d=0;for(e=a.length;d<e;++d)a[d]=Math.round(a[d]*c);c=0;for(d=a.length;c<d;++c)e=a[c],a[c]=0>e?~(e<<1):e<<1;c="";d=0;for(e=a.length;d<e;++d){for(var f=a[d],g,h="";32<=f;)g=(32|f&31)+63,h+=String.fromCharCode(g),f>>=5;h+=String.fromCharCode(f+63);c+=h}return c}
function Tr(a,b){var c=b?b:1E5,d=[],e=0,f=0,g,h;g=0;for(h=a.length;g<h;++g){var l=a.charCodeAt(g)-63,e=e|(l&31)<<f;32>l?(d.push(e),f=e=0):f+=5}e=0;for(f=d.length;e<f;++e)g=d[e],d[e]=g&1?~(g>>1):g>>1;e=0;for(f=d.length;e<f;++e)d[e]/=c;return d}k=Pr.prototype;k.td=function(a,b){var c=this.vd(a,b);return new Am(c)};k.Jf=function(a,b){return[this.td(a,b)]};k.vd=function(a,b){var c=Tc(this.a),d=Sr(a,c,this.b);Or(d,d.length,c,d);c=fd(d,0,d.length,c);return $m(new P(c,this.a),!1,Zm(this,b))};
k.Ge=function(a,b){var c=a.Y();if(c)return this.Dd(c,b);la(!1,40);return""};k.zi=function(a,b){return this.Ge(a[0],b)};k.Dd=function(a,b){a=$m(a,!0,Zm(this,b));var c=a.la(),d=a.ua();Or(c,c.length,d,c);return Qr(c,d,this.b)};function Ur(a){a=a?a:{};this.defaultDataProjection=null;this.defaultDataProjection=vc(a.defaultDataProjection?a.defaultDataProjection:"EPSG:4326")}w(Ur,an);function Vr(a,b){var c=[],d,e,f,g;f=0;for(g=a.length;f<g;++f)d=a[f],0<f&&c.pop(),0<=d?e=b[d]:e=b[~d].slice().reverse(),c.push.apply(c,e);d=0;for(e=c.length;d<e;++d)c[d]=c[d].slice();return c}function Wr(a,b,c,d,e){a=a.geometries;var f=[],g,h;g=0;for(h=a.length;g<h;++g)f[g]=Xr(a[g],b,c,d,e);return f}
function Xr(a,b,c,d,e){var f=a.type,g=Yr[f];b="Point"===f||"MultiPoint"===f?g(a,c,d):g(a,b);c=new Am;c.Xa($m(b,!1,e));void 0!==a.id&&c.Xb(a.id);a.properties&&c.G(a.properties);return c}
Ur.prototype.If=function(a,b){if("Topology"==a.type){var c,d=null,e=null;a.transform&&(c=a.transform,d=c.scale,e=c.translate);var f=a.arcs;if(c){c=d;var g=e,h,l;h=0;for(l=f.length;h<l;++h){var m=f[h],n=c,p=g,q=0,t=0,v,u,y;u=0;for(y=m.length;u<y;++u)v=m[u],q+=v[0],t+=v[1],v[0]=q,v[1]=t,Zr(v,n,p)}}c=[];g=Ba(a.objects);h=0;for(l=g.length;h<l;++h)"GeometryCollection"===g[h].type?(m=g[h],c.push.apply(c,Wr(m,f,d,e,b))):(m=g[h],c.push(Xr(m,f,d,e,b)));return c}return[]};
function Zr(a,b,c){a[0]=a[0]*b[0]+c[0];a[1]=a[1]*b[1]+c[1]}Ur.prototype.Ra=function(){return this.defaultDataProjection};
var Yr={Point:function(a,b,c){a=a.coordinates;b&&c&&Zr(a,b,c);return new C(a)},LineString:function(a,b){var c=Vr(a.arcs,b);return new P(c)},Polygon:function(a,b){var c=[],d,e;d=0;for(e=a.arcs.length;d<e;++d)c[d]=Vr(a.arcs[d],b);return new D(c)},MultiPoint:function(a,b,c){a=a.coordinates;var d,e;if(b&&c)for(d=0,e=a.length;d<e;++d)Zr(a[d],b,c);return new R(a)},MultiLineString:function(a,b){var c=[],d,e;d=0;for(e=a.arcs.length;d<e;++d)c[d]=Vr(a.arcs[d],b);return new Q(c)},MultiPolygon:function(a,b){var c=
[],d,e,f,g,h,l;h=0;for(l=a.arcs.length;h<l;++h){d=a.arcs[h];e=[];f=0;for(g=d.length;f<g;++f)e[f]=Vr(d[f],b);c[h]=e}return new S(c)}};function $r(a){a=a?a:{};this.i=a.featureType;this.g=a.featureNS;this.b=a.gmlFormat?a.gmlFormat:new Pn;this.c=a.schemaLocation?a.schemaLocation:"http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.1.0/wfs.xsd";zn.call(this)}w($r,zn);$r.prototype.nc=function(a,b){var c={featureType:this.i,featureNS:this.g};za(c,Ym(this,a,b?b:{}));c=[c];this.b.b["http://www.opengis.net/gml"].featureMember=Km(Cn.prototype.ud);(c=O([],this.b.b,a,c,this.b))||(c=[]);return c};
$r.prototype.j=function(a){if(Gm(a))return as(a);if(Hm(a))return O({},bs,a,[]);if("string"===typeof a)return a=Im(a),as(a)};$r.prototype.l=function(a){if(Gm(a))return cs(this,a);if(Hm(a))return ds(this,a);if("string"===typeof a)return a=Im(a),cs(this,a)};function cs(a,b){for(var c=b.firstChild;c;c=c.nextSibling)if(c.nodeType==Node.ELEMENT_NODE)return ds(a,c)}var es={"http://www.opengis.net/gml":{boundedBy:K(Cn.prototype.xe,"bounds")}};
function ds(a,b){var c={},d=Kn(b.getAttribute("numberOfFeatures"));c.numberOfFeatures=d;return O(c,es,b,[],a.b)}
var fs={"http://www.opengis.net/wfs":{totalInserted:K(Jn),totalUpdated:K(Jn),totalDeleted:K(Jn)}},gs={"http://www.opengis.net/ogc":{FeatureId:Km(function(a){return a.getAttribute("fid")})}},hs={"http://www.opengis.net/wfs":{Feature:function(a,b){Sm(gs,a,b)}}},bs={"http://www.opengis.net/wfs":{TransactionSummary:K(function(a,b){return O({},fs,a,b)},"transactionSummary"),InsertResults:K(function(a,b){return O([],hs,a,b)},"insertIds")}};
function as(a){for(a=a.firstChild;a;a=a.nextSibling)if(a.nodeType==Node.ELEMENT_NODE)return O({},bs,a,[])}var is={"http://www.opengis.net/wfs":{PropertyName:L(Mn)}};function js(a,b){var c=Dm("http://www.opengis.net/ogc","Filter"),d=Dm("http://www.opengis.net/ogc","FeatureId");c.appendChild(d);d.setAttribute("fid",b);a.appendChild(c)}
var ks={"http://www.opengis.net/wfs":{Insert:L(function(a,b,c){var d=c[c.length-1],d=Dm(d.featureNS,d.featureType);a.appendChild(d);Pn.prototype.yi(d,b,c)}),Update:L(function(a,b,c){var d=c[c.length-1];la(void 0!==b.Oa(),27);var e=d.featureType,f=d.featurePrefix,f=f?f:"feature",g=d.featureNS;a.setAttribute("typeName",f+":"+e);a.setAttributeNS("http://www.w3.org/2000/xmlns/","xmlns:"+f,g);e=b.Oa();if(void 0!==e){for(var f=b.P(),g=[],h=0,l=f.length;h<l;h++){var m=b.get(f[h]);void 0!==m&&g.push({name:f[h],
value:m})}Tm({node:a,srsName:d.srsName},ks,Om("Property"),g,c);js(a,e)}}),Delete:L(function(a,b,c){var d=c[c.length-1];la(void 0!==b.Oa(),26);c=d.featureType;var e=d.featurePrefix,e=e?e:"feature",d=d.featureNS;a.setAttribute("typeName",e+":"+c);a.setAttributeNS("http://www.w3.org/2000/xmlns/","xmlns:"+e,d);b=b.Oa();void 0!==b&&js(a,b)}),Property:L(function(a,b,c){var d=Dm("http://www.opengis.net/wfs","Name");a.appendChild(d);Mn(d,b.name);void 0!==b.value&&null!==b.value&&(d=Dm("http://www.opengis.net/wfs",
"Value"),a.appendChild(d),b.value instanceof Qc?Pn.prototype.Zc(d,b.value,c):Mn(d,b.value))}),Native:L(function(a,b){b.tp&&a.setAttribute("vendorId",b.tp);void 0!==b.Vo&&a.setAttribute("safeToIgnore",b.Vo);void 0!==b.value&&Mn(a,b.value)})}};function ls(a,b,c){a={node:a};var d=b.b;Tm(a,ms,Om(d.Kb),[d],c);b=b.a;Tm(a,ms,Om(b.Kb),[b],c)}function ns(a,b){void 0!==b.a&&a.setAttribute("matchCase",b.a.toString());os(a,b.b);ps("Literal",a,""+b.g)}
function ps(a,b,c){a=Dm("http://www.opengis.net/ogc",a);Mn(a,c);b.appendChild(a)}function os(a,b){ps("PropertyName",a,b)}
var ms={"http://www.opengis.net/wfs":{Query:L(function(a,b,c){var d=c[c.length-1],e=d.featurePrefix,f=d.featureNS,g=d.propertyNames,h=d.srsName;a.setAttribute("typeName",(e?e+":":"")+b);h&&a.setAttribute("srsName",h);f&&a.setAttributeNS("http://www.w3.org/2000/xmlns/","xmlns:"+e,f);b=za({},d);b.node=a;Tm(b,is,Om("PropertyName"),g,c);if(d=d.filter)g=Dm("http://www.opengis.net/ogc","Filter"),a.appendChild(g),Tm({node:g},ms,Om(d.Kb),[d],c)})},"http://www.opengis.net/ogc":{And:L(ls),Or:L(ls),Not:L(function(a,
b,c){b=b.condition;Tm({node:a},ms,Om(b.Kb),[b],c)}),BBOX:L(function(a,b,c){c[c.length-1].srsName=b.srsName;os(a,b.geometryName);Pn.prototype.Zc(a,b.extent,c)}),Intersects:L(function(a,b,c){c[c.length-1].srsName=b.srsName;os(a,b.geometryName);Pn.prototype.Zc(a,b.geometry,c)}),Within:L(function(a,b,c){c[c.length-1].srsName=b.srsName;os(a,b.geometryName);Pn.prototype.Zc(a,b.geometry,c)}),PropertyIsEqualTo:L(ns),PropertyIsNotEqualTo:L(ns),PropertyIsLessThan:L(ns),PropertyIsLessThanOrEqualTo:L(ns),PropertyIsGreaterThan:L(ns),
PropertyIsGreaterThanOrEqualTo:L(ns),PropertyIsNull:L(function(a,b){os(a,b.b)}),PropertyIsBetween:L(function(a,b){os(a,b.b);ps("LowerBoundary",a,""+b.a);ps("UpperBoundary",a,""+b.g)}),PropertyIsLike:L(function(a,b){a.setAttribute("wildCard",b.i);a.setAttribute("singleChar",b.c);a.setAttribute("escapeChar",b.g);void 0!==b.a&&a.setAttribute("matchCase",b.a.toString());os(a,b.b);ps("Literal",a,""+b.f)})}};
$r.prototype.o=function(a){var b=Dm("http://www.opengis.net/wfs","GetFeature");b.setAttribute("service","WFS");b.setAttribute("version","1.1.0");var c;if(a&&(a.handle&&b.setAttribute("handle",a.handle),a.outputFormat&&b.setAttribute("outputFormat",a.outputFormat),void 0!==a.maxFeatures&&b.setAttribute("maxFeatures",a.maxFeatures),a.resultType&&b.setAttribute("resultType",a.resultType),void 0!==a.startIndex&&b.setAttribute("startIndex",a.startIndex),void 0!==a.count&&b.setAttribute("count",a.count),
c=a.filter,a.bbox)){la(a.geometryName,12);var d=pr(a.geometryName,a.bbox,a.srsName);c?c=or(c,d):c=d}b.setAttributeNS("http://www.w3.org/2001/XMLSchema-instance","xsi:schemaLocation",this.c);c={node:b,srsName:a.srsName,featureNS:a.featureNS?a.featureNS:this.g,featurePrefix:a.featurePrefix,geometryName:a.geometryName,filter:c,propertyNames:a.propertyNames?a.propertyNames:[]};la(Array.isArray(a.featureTypes),11);a=a.featureTypes;c=[c];d=za({},c[c.length-1]);d.node=b;Tm(d,ms,Om("Query"),a,c);return b};
$r.prototype.U=function(a,b,c,d){var e=[],f=Dm("http://www.opengis.net/wfs","Transaction");f.setAttribute("service","WFS");f.setAttribute("version","1.1.0");var g,h;d&&(g=d.gmlOptions?d.gmlOptions:{},d.handle&&f.setAttribute("handle",d.handle));f.setAttributeNS("http://www.w3.org/2001/XMLSchema-instance","xsi:schemaLocation",this.c);a&&(h={node:f,featureNS:d.featureNS,featureType:d.featureType,featurePrefix:d.featurePrefix,srsName:d.srsName},za(h,g),Tm(h,ks,Om("Insert"),a,e));b&&(h={node:f,featureNS:d.featureNS,
featureType:d.featureType,featurePrefix:d.featurePrefix,srsName:d.srsName},za(h,g),Tm(h,ks,Om("Update"),b,e));c&&Tm({node:f,featureNS:d.featureNS,featureType:d.featureType,featurePrefix:d.featurePrefix,srsName:d.srsName},ks,Om("Delete"),c,e);d.nativeElements&&Tm({node:f,featureNS:d.featureNS,featureType:d.featureType,featurePrefix:d.featurePrefix,srsName:d.srsName},ks,Om("Native"),d.nativeElements,e);return f};
$r.prototype.Of=function(a){for(a=a.firstChild;a;a=a.nextSibling)if(a.nodeType==Node.ELEMENT_NODE)return this.Ae(a);return null};$r.prototype.Ae=function(a){if(a.firstElementChild&&a.firstElementChild.firstElementChild)for(a=a.firstElementChild.firstElementChild,a=a.firstElementChild;a;a=a.nextElementSibling)if(0!==a.childNodes.length&&(1!==a.childNodes.length||3!==a.firstChild.nodeType)){var b=[{}];this.b.xe(a,b);return vc(b.pop().srsName)}return null};function qs(a){a=a?a:{};this.defaultDataProjection=null;this.b=void 0!==a.splitCollection?a.splitCollection:!1}w(qs,Ho);function rs(a){a=a.Z();return 0===a.length?"":a[0]+" "+a[1]}function ss(a){a=a.Z();for(var b=[],c=0,d=a.length;c<d;++c)b.push(a[c][0]+" "+a[c][1]);return b.join(",")}function ts(a){var b=[];a=a.Vd();for(var c=0,d=a.length;c<d;++c)b.push("("+ss(a[c])+")");return b.join(",")}function us(a){var b=a.X();a=(0,vs[b])(a);b=b.toUpperCase();return 0===a.length?b+" EMPTY":b+"("+a+")"}
var vs={Point:rs,LineString:ss,Polygon:ts,MultiPoint:function(a){var b=[];a=a.he();for(var c=0,d=a.length;c<d;++c)b.push("("+rs(a[c])+")");return b.join(",")},MultiLineString:function(a){var b=[];a=a.ld();for(var c=0,d=a.length;c<d;++c)b.push("("+ss(a[c])+")");return b.join(",")},MultiPolygon:function(a){var b=[];a=a.Wd();for(var c=0,d=a.length;c<d;++c)b.push("("+ts(a[c])+")");return b.join(",")},GeometryCollection:function(a){var b=[];a=a.df();for(var c=0,d=a.length;c<d;++c)b.push(us(a[c]));return b.join(",")}};
k=qs.prototype;k.td=function(a,b){var c=this.vd(a,b);if(c){var d=new Am;d.Xa(c);return d}return null};k.Jf=function(a,b){var c=[],d=this.vd(a,b);this.b&&"GeometryCollection"==d.X()?c=d.f:c=[d];for(var e=[],f=0,g=c.length;f<g;++f)d=new Am,d.Xa(c[f]),e.push(d);return e};k.vd=function(a,b){var c;c=new ws(new xs(a));c.b=ys(c.a);return(c=zs(c))?$m(c,!1,b):null};k.Ge=function(a,b){var c=a.Y();return c?this.Dd(c,b):""};
k.zi=function(a,b){if(1==a.length)return this.Ge(a[0],b);for(var c=[],d=0,e=a.length;d<e;++d)c.push(a[d].Y());c=new qn(c);return this.Dd(c,b)};k.Dd=function(a,b){return us($m(a,!0,b))};function xs(a){this.a=a;this.b=-1}
function ys(a){var b=a.a.charAt(++a.b),c={position:a.b,value:b};if("("==b)c.type=2;else if(","==b)c.type=5;else if(")"==b)c.type=3;else if("0"<=b&&"9">=b||"."==b||"-"==b){c.type=4;var d,b=a.b,e=!1,f=!1;do{if("."==d)e=!0;else if("e"==d||"E"==d)f=!0;d=a.a.charAt(++a.b)}while("0"<=d&&"9">=d||"."==d&&(void 0===e||!e)||!f&&("e"==d||"E"==d)||f&&("-"==d||"+"==d));a=parseFloat(a.a.substring(b,a.b--));c.value=a}else if("a"<=b&&"z">=b||"A"<=b&&"Z">=b){c.type=1;b=a.b;do d=a.a.charAt(++a.b);while("a"<=d&&"z">=
d||"A"<=d&&"Z">=d);a=a.a.substring(b,a.b--).toUpperCase();c.value=a}else{if(" "==b||"\t"==b||"\r"==b||"\n"==b)return ys(a);if(""===b)c.type=6;else throw Error("Unexpected character: "+b);}return c}function ws(a){this.a=a}k=ws.prototype;k.match=function(a){if(a=this.b.type==a)this.b=ys(this.a);return a};
function zs(a){var b=a.b;if(a.match(1)){var c=b.value;if("GEOMETRYCOLLECTION"==c){a:{if(a.match(2)){b=[];do b.push(zs(a));while(a.match(5));if(a.match(3)){a=b;break a}}else if(As(a)){a=[];break a}throw Error(Bs(a));}return new qn(a)}var d=Cs[c],b=Ds[c];if(!d||!b)throw Error("Invalid geometry type: "+c);a=d.call(a);return new b(a)}throw Error(Bs(a));}k.Df=function(){if(this.match(2)){var a=Es(this);if(this.match(3))return a}else if(As(this))return null;throw Error(Bs(this));};
k.Cf=function(){if(this.match(2)){var a=Fs(this);if(this.match(3))return a}else if(As(this))return[];throw Error(Bs(this));};k.Ef=function(){if(this.match(2)){var a=Gs(this);if(this.match(3))return a}else if(As(this))return[];throw Error(Bs(this));};k.eo=function(){if(this.match(2)){var a;if(2==this.b.type)for(a=[this.Df()];this.match(5);)a.push(this.Df());else a=Fs(this);if(this.match(3))return a}else if(As(this))return[];throw Error(Bs(this));};
k.co=function(){if(this.match(2)){var a=Gs(this);if(this.match(3))return a}else if(As(this))return[];throw Error(Bs(this));};k.fo=function(){if(this.match(2)){for(var a=[this.Ef()];this.match(5);)a.push(this.Ef());if(this.match(3))return a}else if(As(this))return[];throw Error(Bs(this));};function Es(a){for(var b=[],c=0;2>c;++c){var d=a.b;if(a.match(4))b.push(d.value);else break}if(2==b.length)return b;throw Error(Bs(a));}function Fs(a){for(var b=[Es(a)];a.match(5);)b.push(Es(a));return b}
function Gs(a){for(var b=[a.Cf()];a.match(5);)b.push(a.Cf());return b}function As(a){var b=1==a.b.type&&"EMPTY"==a.b.value;b&&(a.b=ys(a.a));return b}function Bs(a){return"Unexpected `"+a.b.value+"` at position "+a.b.position+" in `"+a.a.a+"`"}var Ds={POINT:C,LINESTRING:P,POLYGON:D,MULTIPOINT:R,MULTILINESTRING:Q,MULTIPOLYGON:S},Cs={POINT:ws.prototype.Df,LINESTRING:ws.prototype.Cf,POLYGON:ws.prototype.Ef,MULTIPOINT:ws.prototype.eo,MULTILINESTRING:ws.prototype.co,MULTIPOLYGON:ws.prototype.fo};function Hs(){this.version=void 0}w(Hs,xr);Hs.prototype.a=function(a){for(a=a.firstChild;a;a=a.nextSibling)if(a.nodeType==Node.ELEMENT_NODE)return this.b(a);return null};Hs.prototype.b=function(a){this.version=a.getAttribute("version").trim();return(a=O({version:this.version},Is,a,[]))?a:null};function Js(a,b){return O({},Ks,a,b)}function Ls(a,b){return O({},Ms,a,b)}function Ns(a,b){var c=Js(a,b);if(c){var d=[Kn(a.getAttribute("width")),Kn(a.getAttribute("height"))];c.size=d;return c}}
function Os(a,b){return O([],Ps,a,b)}
var Qs=[null,"http://www.opengis.net/wms"],Is=N(Qs,{Service:K(function(a,b){return O({},Rs,a,b)}),Capability:K(function(a,b){return O({},Ss,a,b)})}),Ss=N(Qs,{Request:K(function(a,b){return O({},Ts,a,b)}),Exception:K(function(a,b){return O([],Us,a,b)}),Layer:K(function(a,b){return O({},Vs,a,b)})}),Rs=N(Qs,{Name:K(T),Title:K(T),Abstract:K(T),KeywordList:K(Os),OnlineResource:K(wr),ContactInformation:K(function(a,b){return O({},Ws,a,b)}),Fees:K(T),AccessConstraints:K(T),LayerLimit:K(Jn),MaxWidth:K(Jn),
MaxHeight:K(Jn)}),Ws=N(Qs,{ContactPersonPrimary:K(function(a,b){return O({},Xs,a,b)}),ContactPosition:K(T),ContactAddress:K(function(a,b){return O({},Ys,a,b)}),ContactVoiceTelephone:K(T),ContactFacsimileTelephone:K(T),ContactElectronicMailAddress:K(T)}),Xs=N(Qs,{ContactPerson:K(T),ContactOrganization:K(T)}),Ys=N(Qs,{AddressType:K(T),Address:K(T),City:K(T),StateOrProvince:K(T),PostCode:K(T),Country:K(T)}),Us=N(Qs,{Format:Km(T)}),Vs=N(Qs,{Name:K(T),Title:K(T),Abstract:K(T),KeywordList:K(Os),CRS:Mm(T),
EX_GeographicBoundingBox:K(function(a,b){var c=O({},Zs,a,b);if(c){var d=c.westBoundLongitude,e=c.southBoundLatitude,f=c.eastBoundLongitude,c=c.northBoundLatitude;return void 0===d||void 0===e||void 0===f||void 0===c?void 0:[d,e,f,c]}}),BoundingBox:Mm(function(a){var b=[In(a.getAttribute("minx")),In(a.getAttribute("miny")),In(a.getAttribute("maxx")),In(a.getAttribute("maxy"))],c=[In(a.getAttribute("resx")),In(a.getAttribute("resy"))];return{crs:a.getAttribute("CRS"),extent:b,res:c}}),Dimension:Mm(function(a){return{name:a.getAttribute("name"),
units:a.getAttribute("units"),unitSymbol:a.getAttribute("unitSymbol"),"default":a.getAttribute("default"),multipleValues:Fn(a.getAttribute("multipleValues")),nearestValue:Fn(a.getAttribute("nearestValue")),current:Fn(a.getAttribute("current")),values:T(a)}}),Attribution:K(function(a,b){return O({},$s,a,b)}),AuthorityURL:Mm(function(a,b){var c=Js(a,b);if(c)return c.name=a.getAttribute("name"),c}),Identifier:Mm(T),MetadataURL:Mm(function(a,b){var c=Js(a,b);if(c)return c.type=a.getAttribute("type"),
c}),DataURL:Mm(Js),FeatureListURL:Mm(Js),Style:Mm(function(a,b){return O({},at,a,b)}),MinScaleDenominator:K(Hn),MaxScaleDenominator:K(Hn),Layer:Mm(function(a,b){var c=b[b.length-1],d=O({},Vs,a,b);if(d){var e=Fn(a.getAttribute("queryable"));void 0===e&&(e=c.queryable);d.queryable=void 0!==e?e:!1;e=Kn(a.getAttribute("cascaded"));void 0===e&&(e=c.cascaded);d.cascaded=e;e=Fn(a.getAttribute("opaque"));void 0===e&&(e=c.opaque);d.opaque=void 0!==e?e:!1;e=Fn(a.getAttribute("noSubsets"));void 0===e&&(e=c.noSubsets);
d.noSubsets=void 0!==e?e:!1;(e=In(a.getAttribute("fixedWidth")))||(e=c.fixedWidth);d.fixedWidth=e;(e=In(a.getAttribute("fixedHeight")))||(e=c.fixedHeight);d.fixedHeight=e;["Style","CRS","AuthorityURL"].forEach(function(a){a in c&&(d[a]=(d[a]||[]).concat(c[a]))});"EX_GeographicBoundingBox BoundingBox Dimension Attribution MinScaleDenominator MaxScaleDenominator".split(" ").forEach(function(a){a in d||(d[a]=c[a])});return d}})}),$s=N(Qs,{Title:K(T),OnlineResource:K(wr),LogoURL:K(Ns)}),Zs=N(Qs,{westBoundLongitude:K(Hn),
eastBoundLongitude:K(Hn),southBoundLatitude:K(Hn),northBoundLatitude:K(Hn)}),Ts=N(Qs,{GetCapabilities:K(Ls),GetMap:K(Ls),GetFeatureInfo:K(Ls)}),Ms=N(Qs,{Format:Mm(T),DCPType:Mm(function(a,b){return O({},bt,a,b)})}),bt=N(Qs,{HTTP:K(function(a,b){return O({},ct,a,b)})}),ct=N(Qs,{Get:K(Js),Post:K(Js)}),at=N(Qs,{Name:K(T),Title:K(T),Abstract:K(T),LegendURL:Mm(Ns),StyleSheetURL:K(Js),StyleURL:K(Js)}),Ks=N(Qs,{Format:K(T),OnlineResource:K(wr)}),Ps=N(Qs,{Keyword:Km(T)});function dt(a){a=a?a:{};this.g="http://mapserver.gis.umn.edu/mapserver";this.b=new Yn;this.c=a.layers?a.layers:null;zn.call(this)}w(dt,zn);
dt.prototype.nc=function(a,b){var c={};b&&za(c,Ym(this,a,b));var d=[c];a.setAttribute("namespaceURI",this.g);var e=a.localName,c=[];if(0!==a.childNodes.length){if("msGMLOutput"==e)for(var f=0,g=a.childNodes.length;f<g;f++){var h=a.childNodes[f];if(h.nodeType===Node.ELEMENT_NODE){var l=d[0],m=h.localName.replace("_layer","");if(!this.c||bb(this.c,m)){m+="_feature";l.featureType=m;l.featureNS=this.g;var n={};n[m]=Km(this.b.Hf,this.b);l=N([l.featureNS,null],n);h.setAttribute("namespaceURI",this.g);(h=
O([],l,h,d,this.b))&&eb(c,h)}}}"FeatureCollection"==e&&(d=O([],this.b.b,a,[{}],this.b))&&(c=d)}return c};function et(){this.g=new yr}w(et,xr);et.prototype.a=function(a){for(a=a.firstChild;a;a=a.nextSibling)if(a.nodeType==Node.ELEMENT_NODE)return this.b(a);return null};et.prototype.b=function(a){var b=a.getAttribute("version").trim(),c=this.g.b(a);if(!c)return null;c.version=b;return(c=O(c,ft,a,[]))?c:null};function gt(a){var b=T(a).split(" ");if(b&&2==b.length)return a=+b[0],b=+b[1],isNaN(a)||isNaN(b)?void 0:[a,b]}
var ht=[null,"http://www.opengis.net/wmts/1.0"],it=[null,"http://www.opengis.net/ows/1.1"],ft=N(ht,{Contents:K(function(a,b){return O({},jt,a,b)})}),jt=N(ht,{Layer:Mm(function(a,b){return O({},kt,a,b)}),TileMatrixSet:Mm(function(a,b){return O({},lt,a,b)})}),kt=N(ht,{Style:Mm(function(a,b){var c=O({},mt,a,b);if(c){var d="true"===a.getAttribute("isDefault");c.isDefault=d;return c}}),Format:Mm(T),TileMatrixSetLink:Mm(function(a,b){return O({},nt,a,b)}),Dimension:Mm(function(a,b){return O({},ot,a,b)}),
ResourceURL:Mm(function(a){var b=a.getAttribute("format"),c=a.getAttribute("template");a=a.getAttribute("resourceType");var d={};b&&(d.format=b);c&&(d.template=c);a&&(d.resourceType=a);return d})},N(it,{Title:K(T),Abstract:K(T),WGS84BoundingBox:K(function(a,b){var c=O([],pt,a,b);return 2!=c.length?void 0:Db(c)}),Identifier:K(T)})),mt=N(ht,{LegendURL:Mm(function(a){var b={};b.format=a.getAttribute("format");b.href=wr(a);return b})},N(it,{Title:K(T),Identifier:K(T)})),nt=N(ht,{TileMatrixSet:K(T)}),
ot=N(ht,{Default:K(T),Value:Mm(T)},N(it,{Identifier:K(T)})),pt=N(it,{LowerCorner:Km(gt),UpperCorner:Km(gt)}),lt=N(ht,{WellKnownScaleSet:K(T),TileMatrix:Mm(function(a,b){return O({},qt,a,b)})},N(it,{SupportedCRS:K(T),Identifier:K(T)})),qt=N(ht,{TopLeftCorner:K(gt),ScaleDenominator:K(Hn),TileWidth:K(Jn),TileHeight:K(Jn),MatrixWidth:K(Jn),MatrixHeight:K(Jn)},N(it,{Identifier:K(T)}));function rt(a){Xa.call(this);a=a||{};this.a=null;this.c=Nc;this.f=void 0;B(this,Za("projection"),this.Il,this);B(this,Za("tracking"),this.Jl,this);void 0!==a.projection&&this.$g(vc(a.projection));void 0!==a.trackingOptions&&this.oi(a.trackingOptions);this.ee(void 0!==a.tracking?a.tracking:!1)}w(rt,Xa);k=rt.prototype;k.ma=function(){this.ee(!1);Xa.prototype.ma.call(this)};k.Il=function(){var a=this.Yg();a&&(this.c=yc(vc("EPSG:4326"),a),this.a&&this.set("position",this.c(this.a)))};
k.Jl=function(){if(Of){var a=this.Zg();a&&void 0===this.f?this.f=ja.navigator.geolocation.watchPosition(this.no.bind(this),this.oo.bind(this),this.Kg()):a||void 0===this.f||(ja.navigator.geolocation.clearWatch(this.f),this.f=void 0)}};
k.no=function(a){a=a.coords;this.set("accuracy",a.accuracy);this.set("altitude",null===a.altitude?void 0:a.altitude);this.set("altitudeAccuracy",null===a.altitudeAccuracy?void 0:a.altitudeAccuracy);this.set("heading",null===a.heading?void 0:sa(a.heading));this.a?(this.a[0]=a.longitude,this.a[1]=a.latitude):this.a=[a.longitude,a.latitude];var b=this.c(this.a);this.set("position",b);this.set("speed",null===a.speed?void 0:a.speed);a=xd(Ei,this.a,a.accuracy);a.rc(this.c);this.set("accuracyGeometry",a);
this.u()};k.oo=function(a){a.type="error";this.ee(!1);this.b(a)};k.Jj=function(){return this.get("accuracy")};k.Kj=function(){return this.get("accuracyGeometry")||null};k.Mj=function(){return this.get("altitude")};k.Nj=function(){return this.get("altitudeAccuracy")};k.Gl=function(){return this.get("heading")};k.Hl=function(){return this.get("position")};k.Yg=function(){return this.get("projection")};k.tk=function(){return this.get("speed")};k.Zg=function(){return this.get("tracking")};k.Kg=function(){return this.get("trackingOptions")};
k.$g=function(a){this.set("projection",a)};k.ee=function(a){this.set("tracking",a)};k.oi=function(a){this.set("trackingOptions",a)};function st(a,b,c){Sc.call(this);this.Uf(a,b?b:0,c)}w(st,Sc);k=st.prototype;k.clone=function(){var a=new st(null),b=this.B.slice();Uc(a,this.ka,b);a.u();return a};k.vb=function(a,b,c,d){var e=this.B;a-=e[0];var f=b-e[1];b=a*a+f*f;if(b<d){if(0===b)for(d=0;d<this.a;++d)c[d]=e[d];else for(d=this.vf()/Math.sqrt(b),c[0]=e[0]+d*a,c[1]=e[1]+d*f,d=2;d<this.a;++d)c[d]=e[d];c.length=this.a;return b}return d};k.zc=function(a,b){var c=this.B,d=a-c[0],c=b-c[1];return d*d+c*c<=tt(this)};
k.qd=function(){return this.B.slice(0,this.a)};k.Nd=function(a){var b=this.B,c=b[this.a]-b[0];return Qb(b[0]-c,b[1]-c,b[0]+c,b[1]+c,a)};k.vf=function(){return Math.sqrt(tt(this))};function tt(a){var b=a.B[a.a]-a.B[0];a=a.B[a.a+1]-a.B[1];return b*b+a*a}k.X=function(){return"Circle"};k.La=function(a){var b=this.C();return kc(a,b)?(b=this.qd(),a[0]<=b[0]&&a[2]>=b[0]||a[1]<=b[1]&&a[3]>=b[1]?!0:Xb(a,this.jb,this)):!1};
k.em=function(a){var b=this.a,c=this.B[b]-this.B[0],d=a.slice();d[b]=d[0]+c;for(c=1;c<b;++c)d[b+c]=a[c];Uc(this,this.ka,d);this.u()};k.Uf=function(a,b,c){if(a){Vc(this,c,a,0);this.B||(this.B=[]);c=this.B;a=cd(c,a);c[a++]=c[0]+b;var d;b=1;for(d=this.a;b<d;++b)c[a++]=c[b];c.length=a}else Uc(this,"XY",null);this.u()};k.fm=function(a){this.B[this.a]=this.B[0]+a;this.u()};function ut(a,b,c){for(var d=[],e=a(0),f=a(1),g=b(e),h=b(f),l=[f,e],m=[h,g],n=[1,0],p={},q=1E5,t,v,u,y,E;0<--q&&0<n.length;)u=n.pop(),e=l.pop(),g=m.pop(),f=u.toString(),f in p||(d.push(g[0],g[1]),p[f]=!0),y=n.pop(),f=l.pop(),h=m.pop(),E=(u+y)/2,t=a(E),v=b(t),qa(v[0],v[1],g[0],g[1],h[0],h[1])<c?(d.push(h[0],h[1]),f=y.toString(),p[f]=!0):(n.push(y,E,E,u),m.push(h,v,v,g),l.push(f,t,t,e));return d}function vt(a,b,c,d,e){var f=vc("EPSG:4326");return ut(function(d){return[a,b+(c-b)*d]},Mc(f,d),e)}
function wt(a,b,c,d,e){var f=vc("EPSG:4326");return ut(function(d){return[b+(c-b)*d,a]},Mc(f,d),e)};function xt(a){a=a||{};this.c=this.j=null;this.g=this.i=Infinity;this.f=this.l=-Infinity;this.A=this.U=Infinity;this.D=this.H=-Infinity;this.Ba=void 0!==a.targetSize?a.targetSize:100;this.O=void 0!==a.maxLines?a.maxLines:100;this.b=[];this.a=[];this.Aa=void 0!==a.strokeStyle?a.strokeStyle:yt;this.v=this.o=void 0;this.s=null;this.setMap(void 0!==a.map?a.map:null)}var yt=new Yi({color:"rgba(0,0,0,0.2)"}),zt=[90,45,30,20,10,5,2,1,.5,.2,.1,.05,.01,.005,.002,.001];
function At(a,b,c,d,e,f,g){var h=g;b=vt(b,c,d,a.c,e);h=void 0!==a.b[h]?a.b[h]:new P(null);h.ba("XY",b);kc(h.C(),f)&&(a.b[g++]=h);return g}function Bt(a,b,c,d,e){var f=e;b=wt(b,a.f,a.g,a.c,c);f=void 0!==a.a[f]?a.a[f]:new P(null);f.ba("XY",b);kc(f.C(),d)&&(a.a[e++]=f);return e}k=xt.prototype;k.Kl=function(){return this.j};k.fk=function(){return this.b};k.mk=function(){return this.a};
k.Pg=function(a){var b=a.vectorContext,c=a.frameState,d=c.extent;a=c.viewState;var e=a.center,f=a.projection,g=a.resolution;a=c.pixelRatio;a=g*g/(4*a*a);if(!this.c||!Lc(this.c,f)){var h=vc("EPSG:4326"),l=f.C(),m=f.i,n=Pc(m,h,f),p=m[2],q=m[1],t=m[0],v=n[3],u=n[2],y=n[1],n=n[0];this.i=m[3];this.g=p;this.l=q;this.f=t;this.U=v;this.A=u;this.H=y;this.D=n;this.o=Mc(h,f);this.v=Mc(f,h);this.s=this.v(gc(l));this.c=f}f.a&&(f=f.C(),h=ec(f),c=c.focus[0],c<f[0]||c>f[2])&&(c=h*Math.ceil((f[0]-c)/h),d=[d[0]+c,
d[1],d[2]+c,d[3]]);c=this.s[0];f=this.s[1];h=-1;m=Math.pow(this.Ba*g,2);p=[];q=[];g=0;for(l=zt.length;g<l;++g){t=zt[g]/2;p[0]=c-t;p[1]=f-t;q[0]=c+t;q[1]=f+t;this.o(p,p);this.o(q,q);t=Math.pow(q[0]-p[0],2)+Math.pow(q[1]-p[1],2);if(t<=m)break;h=zt[g]}g=h;if(-1==g)this.b.length=this.a.length=0;else{c=this.v(e);e=c[0];c=c[1];f=this.O;h=[Math.max(d[0],this.D),Math.max(d[1],this.H),Math.min(d[2],this.A),Math.min(d[3],this.U)];h=Pc(h,this.c,"EPSG:4326");m=h[3];q=h[1];e=Math.floor(e/g)*g;p=ma(e,this.f,this.g);
l=At(this,p,q,m,a,d,0);for(h=0;p!=this.f&&h++<f;)p=Math.max(p-g,this.f),l=At(this,p,q,m,a,d,l);p=ma(e,this.f,this.g);for(h=0;p!=this.g&&h++<f;)p=Math.min(p+g,this.g),l=At(this,p,q,m,a,d,l);this.b.length=l;c=Math.floor(c/g)*g;e=ma(c,this.l,this.i);l=Bt(this,e,a,d,0);for(h=0;e!=this.l&&h++<f;)e=Math.max(e-g,this.l),l=Bt(this,e,a,d,l);e=ma(c,this.l,this.i);for(h=0;e!=this.i&&h++<f;)e=Math.min(e+g,this.i),l=Bt(this,e,a,d,l);this.a.length=l}b.Wb(null,this.Aa);a=0;for(e=this.b.length;a<e;++a)g=this.b[a],
b.hd(g,null);a=0;for(e=this.a.length;a<e;++a)g=this.a[a],b.hd(g,null)};k.setMap=function(a){this.j&&(this.j.J("postcompose",this.Pg,this),this.j.render());a&&(a.I("postcompose",this.Pg,this),a.render());this.j=a};function Ct(a,b,c,d,e,f,g){rj.call(this,a,b,c,0,d);this.o=e;this.g=new Image;null!==f&&(this.g.crossOrigin=f);this.i={};this.c=null;this.state=0;this.j=g}w(Ct,rj);Ct.prototype.a=function(a){if(void 0!==a){var b;a=x(a);if(a in this.i)return this.i[a];Ca(this.i)?b=this.g:b=this.g.cloneNode(!1);return this.i[a]=b}return this.g};Ct.prototype.s=function(){this.state=3;this.c.forEach(Ea);this.c=null;sj(this)};
Ct.prototype.v=function(){void 0===this.resolution&&(this.resolution=fc(this.extent)/this.g.height);this.state=2;this.c.forEach(Ea);this.c=null;sj(this)};Ct.prototype.load=function(){if(0==this.state||3==this.state)this.state=1,sj(this),this.c=[Ja(this.g,"error",this.s,this),Ja(this.g,"load",this.v,this)],this.j(this,this.o)};function Dt(a,b,c,d,e){qh.call(this,a,b);this.s=c;this.g=new Image;null!==d&&(this.g.crossOrigin=d);this.c={};this.o=null;this.v=e}w(Dt,qh);k=Dt.prototype;k.ma=function(){1==this.state&&Et(this);this.a&&Oa(this.a);this.state=5;rh(this);qh.prototype.ma.call(this)};k.ab=function(a){if(void 0!==a){var b=x(a);if(b in this.c)return this.c[b];a=Ca(this.c)?this.g:this.g.cloneNode(!1);return this.c[b]=a}return this.g};k.Ya=function(){return this.s};k.Ll=function(){this.state=3;Et(this);rh(this)};
k.Ml=function(){this.state=this.g.naturalWidth&&this.g.naturalHeight?2:4;Et(this);rh(this)};k.load=function(){if(0==this.state||3==this.state)this.state=1,rh(this),this.o=[Ja(this.g,"error",this.Ll,this),Ja(this.g,"load",this.Ml,this)],this.v(this,this.s)};function Et(a){a.o.forEach(Ea);a.o=null};function Ft(a){a=a?a:{};Bh.call(this,{handleEvent:nc});this.c=a.formatConstructors?a.formatConstructors:[];this.o=a.projection?vc(a.projection):null;this.a=null;this.target=a.target?a.target:null}w(Ft,Bh);function Gt(a){a=a.dataTransfer.files;var b,c,d;b=0;for(c=a.length;b<c;++b){d=a.item(b);var e=new FileReader;e.addEventListener("load",this.j.bind(this,d));e.readAsText(d)}}function Ht(a){a.stopPropagation();a.preventDefault();a.dataTransfer.dropEffect="copy"}
Ft.prototype.j=function(a,b){var c=b.target.result,d=this.v,e=this.o;e||(e=d.aa().j);var d=this.c,f=[],g,h;g=0;for(h=d.length;g<h;++g){var l=new d[g];var m={featureProjection:e};try{f=l.Fa(c,m)}catch(n){f=null}if(f&&0<f.length)break}this.b(new It(Jt,a,f,e))};Ft.prototype.setMap=function(a){this.a&&(this.a.forEach(Ea),this.a=null);Bh.prototype.setMap.call(this,a);a&&(a=this.target?this.target:a.a,this.a=[B(a,"drop",Gt,this),B(a,"dragenter",Ht,this),B(a,"dragover",Ht,this),B(a,"drop",Ht,this)])};
var Jt="addfeatures";function It(a,b,c,d){Pa.call(this,a);this.features=c;this.file=b;this.projection=d}w(It,Pa);function Kt(a){a=a?a:{};Qh.call(this,{handleDownEvent:Lt,handleDragEvent:Mt,handleUpEvent:Nt});this.s=a.condition?a.condition:Mh;this.a=this.c=void 0;this.o=0;this.A=void 0!==a.duration?a.duration:400}w(Kt,Qh);function Mt(a){if(Oh(a)){var b=a.map,c=b.kb(),d=a.pixel;a=d[0]-c[0]/2;d=c[1]/2-d[1];c=Math.atan2(d,a);a=Math.sqrt(a*a+d*d);d=b.aa();if(void 0!==this.c){var e=c-this.c;Ch(b,d,d.Ma()-e)}this.c=c;void 0!==this.a&&(c=this.a*(d.$()/a),Eh(b,d,c));void 0!==this.a&&(this.o=this.a/a);this.a=a}}
function Nt(a){if(!Oh(a))return!0;a=a.map;var b=a.aa();Kd(b,-1);var c=this.o-1,d=b.Ma(),d=b.constrainRotation(d,0);Ch(a,b,d,void 0,void 0);var d=b.$(),e=this.A,d=b.constrainResolution(d,0,c);Eh(a,b,d,void 0,e);this.o=0;return!1}function Lt(a){return Oh(a)&&this.s(a)?(Kd(a.map.aa(),1),this.a=this.c=void 0,!0):!1};function Ot(){return[[-Infinity,-Infinity,Infinity,Infinity]]};(function(){var a={},b={ja:a};(function(c){if("object"===typeof a&&"undefined"!==typeof b)b.ja=c();else{var d;"undefined"!==typeof window?d=window:"undefined"!==typeof global?d=global:"undefined"!==typeof self?d=self:d=this;d.Pp=c()}})(function(){return function d(a,b,g){function h(m,p){if(!b[m]){if(!a[m]){var q="function"==typeof require&&require;if(!p&&q)return q(m,!0);if(l)return l(m,!0);q=Error("Cannot find module '"+m+"'");throw q.code="MODULE_NOT_FOUND",q;}q=b[m]={ja:{}};a[m][0].call(q.ja,function(b){var d=
a[m][1][b];return h(d?d:b)},q,q.ja,d,a,b,g)}return b[m].ja}for(var l="function"==typeof require&&require,m=0;m<g.length;m++)h(g[m]);return h}({1:[function(a,b){function f(a,b,d,e,q){d=d||0;e=e||a.length-1;for(q=q||h;e>d;){if(600<e-d){var t=e-d+1,v=b-d+1,u=Math.log(t),y=.5*Math.exp(2*u/3),u=.5*Math.sqrt(u*y*(t-y)/t)*(0>v-t/2?-1:1);f(a,b,Math.max(d,Math.floor(b-v*y/t+u)),Math.min(e,Math.floor(b+(t-v)*y/t+u)),q)}t=a[b];v=d;y=e;g(a,d,b);for(0<q(a[e],t)&&g(a,d,e);v<y;){g(a,v,y);v++;for(y--;0>q(a[v],t);)v++;
for(;0<q(a[y],t);)y--}0===q(a[d],t)?g(a,d,y):(y++,g(a,y,e));y<=b&&(d=y+1);b<=y&&(e=y-1)}}function g(a,b,d){var e=a[b];a[b]=a[d];a[d]=e}function h(a,b){return a<b?-1:a>b?1:0}b.ja=f},{}],2:[function(a,b){function f(a,b){if(!(this instanceof f))return new f(a,b);this.Re=Math.max(4,a||9);this.gg=Math.max(2,Math.ceil(.4*this.Re));b&&this.ij(b);this.clear()}function g(a,b){h(a,0,a.children.length,b,a)}function h(a,b,d,e,f){f||(f=u(null));f.ca=Infinity;f.fa=Infinity;f.ea=-Infinity;f.ia=-Infinity;for(var g;b<
d;b++)g=a.children[b],l(f,a.Wa?e(g):g);return f}function l(a,b){a.ca=Math.min(a.ca,b.ca);a.fa=Math.min(a.fa,b.fa);a.ea=Math.max(a.ea,b.ea);a.ia=Math.max(a.ia,b.ia)}function m(a,b){return a.ca-b.ca}function n(a,b){return a.fa-b.fa}function p(a){return(a.ea-a.ca)*(a.ia-a.fa)}function q(a){return a.ea-a.ca+(a.ia-a.fa)}function t(a,b){return a.ca<=b.ca&&a.fa<=b.fa&&b.ea<=a.ea&&b.ia<=a.ia}function v(a,b){return b.ca<=a.ea&&b.fa<=a.ia&&b.ea>=a.ca&&b.ia>=a.fa}function u(a){return{children:a,height:1,Wa:!0,
ca:Infinity,fa:Infinity,ea:-Infinity,ia:-Infinity}}function y(a,b,d,e,f){for(var g=[b,d],h;g.length;)d=g.pop(),b=g.pop(),d-b<=e||(h=b+Math.ceil((d-b)/e/2)*e,E(a,h,b,d,f),g.push(b,h,h,d))}b.ja=f;var E=a("quickselect");f.prototype={all:function(){return this.bg(this.data,[])},search:function(a){var b=this.data,d=[],e=this.nb;if(!v(a,b))return d;for(var f=[],g,h,l,m;b;){g=0;for(h=b.children.length;g<h;g++)l=b.children[g],m=b.Wa?e(l):l,v(a,m)&&(b.Wa?d.push(l):t(a,m)?this.bg(l,d):f.push(l));b=f.pop()}return d},
load:function(a){if(!a||!a.length)return this;if(a.length<this.gg){for(var b=0,d=a.length;b<d;b++)this.Ca(a[b]);return this}a=this.dg(a.slice(),0,a.length-1,0);this.data.children.length?this.data.height===a.height?this.ig(this.data,a):(this.data.height<a.height&&(b=this.data,this.data=a,a=b),this.fg(a,this.data.height-a.height-1,!0)):this.data=a;return this},Ca:function(a){a&&this.fg(a,this.data.height-1);return this},clear:function(){this.data=u([]);return this},remove:function(a,b){if(!a)return this;
for(var d=this.data,e=this.nb(a),f=[],g=[],h,l,m,n;d||f.length;){d||(d=f.pop(),l=f[f.length-1],h=g.pop(),n=!0);if(d.Wa){a:{m=a;var p=d.children,q=b;if(q){for(var u=0;u<p.length;u++)if(q(m,p[u])){m=u;break a}m=-1}else m=p.indexOf(m)}if(-1!==m){d.children.splice(m,1);f.push(d);this.gj(f);break}}n||d.Wa||!t(d,e)?l?(h++,d=l.children[h],n=!1):d=null:(f.push(d),g.push(h),h=0,l=d,d=d.children[0])}return this},nb:function(a){return a},Te:m,Ue:n,toJSON:function(){return this.data},bg:function(a,b){for(var d=
[];a;)a.Wa?b.push.apply(b,a.children):d.push.apply(d,a.children),a=d.pop();return b},dg:function(a,b,d,e){var f=d-b+1,h=this.Re,l;if(f<=h)return l=u(a.slice(b,d+1)),g(l,this.nb),l;e||(e=Math.ceil(Math.log(f)/Math.log(h)),h=Math.ceil(f/Math.pow(h,e-1)));l=u([]);l.Wa=!1;l.height=e;var f=Math.ceil(f/h),h=f*Math.ceil(Math.sqrt(h)),m,n,p;for(y(a,b,d,h,this.Te);b<=d;b+=h)for(n=Math.min(b+h-1,d),y(a,b,n,f,this.Ue),m=b;m<=n;m+=f)p=Math.min(m+f-1,n),l.children.push(this.dg(a,m,p,e-1));g(l,this.nb);return l},
fj:function(a,b,d,e){for(var f,g,h,l,m,n,q,t;;){e.push(b);if(b.Wa||e.length-1===d)break;q=t=Infinity;f=0;for(g=b.children.length;f<g;f++)h=b.children[f],m=p(h),n=(Math.max(h.ea,a.ea)-Math.min(h.ca,a.ca))*(Math.max(h.ia,a.ia)-Math.min(h.fa,a.fa))-m,n<t?(t=n,q=m<q?m:q,l=h):n===t&&m<q&&(q=m,l=h);b=l||b.children[0]}return b},fg:function(a,b,d){var e=this.nb;d=d?a:e(a);var e=[],f=this.fj(d,this.data,b,e);f.children.push(a);for(l(f,d);0<=b;)if(e[b].children.length>this.Re)this.oj(e,b),b--;else break;this.cj(d,
e,b)},oj:function(a,b){var d=a[b],e=d.children.length,f=this.gg;this.dj(d,f,e);e=this.ej(d,f,e);e=u(d.children.splice(e,d.children.length-e));e.height=d.height;e.Wa=d.Wa;g(d,this.nb);g(e,this.nb);b?a[b-1].children.push(e):this.ig(d,e)},ig:function(a,b){this.data=u([a,b]);this.data.height=a.height+1;this.data.Wa=!1;g(this.data,this.nb)},ej:function(a,b,d){var e,f,g,l,m,n,q;m=n=Infinity;for(e=b;e<=d-b;e++)f=h(a,0,e,this.nb),g=h(a,e,d,this.nb),l=Math.max(0,Math.min(f.ea,g.ea)-Math.max(f.ca,g.ca))*Math.max(0,
Math.min(f.ia,g.ia)-Math.max(f.fa,g.fa)),f=p(f)+p(g),l<m?(m=l,q=e,n=f<n?f:n):l===m&&f<n&&(n=f,q=e);return q},dj:function(a,b,d){var e=a.Wa?this.Te:m,f=a.Wa?this.Ue:n,g=this.cg(a,b,d,e);b=this.cg(a,b,d,f);g<b&&a.children.sort(e)},cg:function(a,b,d,e){a.children.sort(e);e=this.nb;var f=h(a,0,b,e),g=h(a,d-b,d,e),m=q(f)+q(g),n,p;for(n=b;n<d-b;n++)p=a.children[n],l(f,a.Wa?e(p):p),m+=q(f);for(n=d-b-1;n>=b;n--)p=a.children[n],l(g,a.Wa?e(p):p),m+=q(g);return m},cj:function(a,b,d){for(;0<=d;d--)l(b[d],a)},
gj:function(a){for(var b=a.length-1,d;0<=b;b--)0===a[b].children.length?0<b?(d=a[b-1].children,d.splice(d.indexOf(a[b]),1)):this.clear():g(a[b],this.nb)},ij:function(a){var b=["return a"," - b",";"];this.Te=new Function("a","b",b.join(a[0]));this.Ue=new Function("a","b",b.join(a[1]));this.nb=new Function("a","return {minX: a"+a[0]+", minY: a"+a[1]+", maxX: a"+a[2]+", maxY: a"+a[3]+"};")}}},{quickselect:1}]},{},[2])(2)});Pq=b.ja})();function Pt(a){this.b=Pq(a);this.a={}}k=Pt.prototype;k.Ca=function(a,b){var c={ca:a[0],fa:a[1],ea:a[2],ia:a[3],value:b};this.b.Ca(c);this.a[x(b)]=c};k.load=function(a,b){for(var c=Array(b.length),d=0,e=b.length;d<e;d++){var f=a[d],g=b[d],f={ca:f[0],fa:f[1],ea:f[2],ia:f[3],value:g};c[d]=f;this.a[x(g)]=f}this.b.load(c)};k.remove=function(a){a=x(a);var b=this.a[a];delete this.a[a];return null!==this.b.remove(b)};
function Qt(a,b,c){var d=a.a[x(c)];Ub([d.ca,d.fa,d.ea,d.ia],b)||(a.remove(c),a.Ca(b,c))}function Rt(a){return a.b.all().map(function(a){return a.value})}function St(a,b){return a.b.search({ca:b[0],fa:b[1],ea:b[2],ia:b[3]}).map(function(a){return a.value})}k.forEach=function(a,b){return Tt(Rt(this),a,b)};function Ut(a,b,c,d){return Tt(St(a,b),c,d)}function Tt(a,b,c){for(var d,e=0,f=a.length;e<f&&!(d=b.call(c,a[e]));e++);return d}k.clear=function(){this.b.clear();this.a={}};
k.C=function(){var a=this.b.data;return[a.ca,a.fa,a.ea,a.ia]};function U(a){a=a||{};Tg.call(this,{attributions:a.attributions,logo:a.logo,projection:void 0,state:"ready",wrapX:void 0!==a.wrapX?a.wrapX:!0});this.T=ha;this.R=a.format;this.W=void 0==a.overlaps?!0:a.overlaps;this.ha=a.url;void 0!==a.loader?this.T=a.loader:void 0!==this.ha&&(la(this.R,7),this.T=Wm(this.ha,this.R));this.Mb=void 0!==a.strategy?a.strategy:Ot;var b=void 0!==a.useSpatialIndex?a.useSpatialIndex:!0;this.a=b?new Pt:null;this.na=new Pt;this.i={};this.j={};this.o={};this.s={};this.c=null;
var c,d;a.features instanceof re?(c=a.features,d=c.a):Array.isArray(a.features)&&(d=a.features);b||void 0!==c||(c=new re(d));void 0!==d&&Vt(this,d);void 0!==c&&Wt(this,c)}w(U,Tg);k=U.prototype;k.ub=function(a){var b=x(a).toString();if(Xt(this,b,a)){Yt(this,b,a);var c=a.Y();c?(b=c.C(),this.a&&this.a.Ca(b,a)):this.i[b]=a;this.b(new Zt("addfeature",a))}this.u()};function Yt(a,b,c){a.s[b]=[B(c,"change",a.Bh,a),B(c,"propertychange",a.Bh,a)]}
function Xt(a,b,c){var d=!0,e=c.Oa();void 0!==e?e.toString()in a.j?d=!1:a.j[e.toString()]=c:(la(!(b in a.o),30),a.o[b]=c);return d}k.Hc=function(a){Vt(this,a);this.u()};function Vt(a,b){var c,d,e,f,g=[],h=[],l=[];d=0;for(e=b.length;d<e;d++)f=b[d],c=x(f).toString(),Xt(a,c,f)&&h.push(f);d=0;for(e=h.length;d<e;d++){f=h[d];c=x(f).toString();Yt(a,c,f);var m=f.Y();m?(c=m.C(),g.push(c),l.push(f)):a.i[c]=f}a.a&&a.a.load(g,l);d=0;for(e=h.length;d<e;d++)a.b(new Zt("addfeature",h[d]))}
function Wt(a,b){var c=!1;B(a,"addfeature",function(a){c||(c=!0,b.push(a.feature),c=!1)});B(a,"removefeature",function(a){c||(c=!0,b.remove(a.feature),c=!1)});B(b,ue,function(a){c||(c=!0,this.ub(a.element),c=!1)},a);B(b,ve,function(a){c||(c=!0,this.pb(a.element),c=!1)},a);a.c=b}
k.clear=function(a){if(a){for(var b in this.s)this.s[b].forEach(Ea);this.c||(this.s={},this.j={},this.o={})}else if(this.a){this.a.forEach(this.Rf,this);for(var c in this.i)this.Rf(this.i[c])}this.c&&this.c.clear();this.a&&this.a.clear();this.na.clear();this.i={};this.b(new Zt("clear"));this.u()};k.ug=function(a,b){if(this.a)return this.a.forEach(a,b);if(this.c)return this.c.forEach(a,b)};function $t(a,b,c){a.xb([b[0],b[1],b[0],b[1]],function(a){if(a.Y().jb(b))return c.call(void 0,a)})}
k.xb=function(a,b,c){if(this.a)return Ut(this.a,a,b,c);if(this.c)return this.c.forEach(b,c)};k.vg=function(a,b,c){return this.xb(a,function(d){if(d.Y().La(a)&&(d=b.call(c,d)))return d})};k.Dg=function(){return this.c};k.me=function(){var a;this.c?a=this.c.a:this.a&&(a=Rt(this.a),Ca(this.i)||eb(a,Ba(this.i)));return a};k.Cg=function(a){var b=[];$t(this,a,function(a){b.push(a)});return b};k.cf=function(a){return St(this.a,a)};
k.yg=function(a,b){var c=a[0],d=a[1],e=null,f=[NaN,NaN],g=Infinity,h=[-Infinity,-Infinity,Infinity,Infinity],l=b?b:nc;Ut(this.a,h,function(a){if(l(a)){var b=a.Y(),p=g;g=b.vb(c,d,f,g);g<p&&(e=a,a=Math.sqrt(g),h[0]=c-a,h[1]=d-a,h[2]=c+a,h[3]=d+a)}});return e};k.C=function(){return this.a.C()};k.Bg=function(a){a=this.j[a.toString()];return void 0!==a?a:null};k.zh=function(){return this.R};k.Ah=function(){return this.ha};
k.Bh=function(a){a=a.target;var b=x(a).toString(),c=a.Y();c?(c=c.C(),b in this.i?(delete this.i[b],this.a&&this.a.Ca(c,a)):this.a&&Qt(this.a,c,a)):b in this.i||(this.a&&this.a.remove(a),this.i[b]=a);c=a.Oa();void 0!==c?(c=c.toString(),b in this.o?(delete this.o[b],this.j[c]=a):this.j[c]!==a&&(au(this,a),this.j[c]=a)):b in this.o||(au(this,a),this.o[b]=a);this.u();this.b(new Zt("changefeature",a))};
k.Nc=function(a,b,c){var d=this.na;a=this.Mb(a,b);var e,f;e=0;for(f=a.length;e<f;++e){var g=a[e];Ut(d,g,function(a){return Nb(a.extent,g)})||(this.T.call(this,g,b,c),d.Ca(g,{extent:g.slice()}))}};k.pb=function(a){var b=x(a).toString();b in this.i?delete this.i[b]:this.a&&this.a.remove(a);this.Rf(a);this.u()};k.Rf=function(a){var b=x(a).toString();this.s[b].forEach(Ea);delete this.s[b];var c=a.Oa();void 0!==c?delete this.j[c.toString()]:delete this.o[b];this.b(new Zt("removefeature",a))};
function au(a,b){for(var c in a.j)if(a.j[c]===b){delete a.j[c];break}}function Zt(a,b){Pa.call(this,a);this.feature=b}w(Zt,Pa);function bu(a,b){Pa.call(this,a);this.feature=b}w(bu,Pa);
function cu(a){Qh.call(this,{handleDownEvent:du,handleEvent:eu,handleUpEvent:fu});this.pa=null;this.R=!1;this.Mb=a.source?a.source:null;this.sb=a.features?a.features:null;this.Bj=a.snapTolerance?a.snapTolerance:12;this.W=a.type;this.c=gu(this.W);this.Ia=a.minPoints?a.minPoints:this.c===hu?3:2;this.Ha=a.maxPoints?a.maxPoints:Infinity;this.Me=a.finishCondition?a.finishCondition:nc;var b=a.geometryFunction;if(!b)if("Circle"===this.W)b=function(a,b){var c=b?b:new st([NaN,NaN]);c.Uf(a[0],Math.sqrt(Ab(a[0],
a[1])));return c};else{var c,b=this.c;b===iu?c=C:b===ju?c=P:b===hu&&(c=D);b=function(a,b){var f=b;f?f.sa(a):f=new c(a);return f}}this.D=b;this.T=this.A=this.a=this.O=this.o=this.s=null;this.Cj=a.clickTolerance?a.clickTolerance*a.clickTolerance:36;this.na=new F({source:new U({useSpatialIndex:!1,wrapX:a.wrapX?a.wrapX:!1}),style:a.style?a.style:ku()});this.tb=a.geometryName;this.yj=a.condition?a.condition:Lh;this.za=a.freehandCondition?a.freehandCondition:Mh;B(this,Za("active"),this.vi,this)}w(cu,Qh);
function ku(){var a=dj();return function(b){return a[b.Y().X()]}}k=cu.prototype;k.setMap=function(a){Qh.prototype.setMap.call(this,a);this.vi()};function eu(a){this.c!==ju&&this.c!==hu||!this.za(a)||(this.R=!0);var b=!this.R;this.R&&a.type===Og?(lu(this,a),b=!1):a.type===Ng?b=mu(this,a):a.type===Hg&&(b=!1);return Rh.call(this,a)&&b}function du(a){return this.yj(a)?(this.pa=a.pixel,!0):this.R?(this.pa=a.pixel,this.s||nu(this,a),!0):!1}
function fu(a){this.R=!1;var b=this.pa,c=a.pixel,d=b[0]-c[0],b=b[1]-c[1],c=!0;d*d+b*b<=this.Cj&&(mu(this,a),this.s?this.c===ou?this.jd():pu(this,a)?this.Me(a)&&this.jd():lu(this,a):(nu(this,a),this.c===iu&&this.jd()),c=!1);return c}
function mu(a,b){if(a.s){var c=b.coordinate,d=a.o.Y(),e;a.c===iu?e=a.a:a.c===hu?(e=a.a[0],e=e[e.length-1],pu(a,b)&&(c=a.s.slice())):(e=a.a,e=e[e.length-1]);e[0]=c[0];e[1]=c[1];a.D(a.a,d);a.O&&a.O.Y().sa(c);d instanceof D&&a.c!==hu?(a.A||(a.A=new Am(new P(null))),d=d.Fg(0),c=a.A.Y(),c.ba(d.ka,d.la())):a.T&&(c=a.A.Y(),c.sa(a.T));qu(a)}else c=b.coordinate.slice(),a.O?a.O.Y().sa(c):(a.O=new Am(new C(c)),qu(a));return!0}
function pu(a,b){var c=!1;if(a.o){var d=!1,e=[a.s];a.c===ju?d=a.a.length>a.Ia:a.c===hu&&(d=a.a[0].length>a.Ia,e=[a.a[0][0],a.a[0][a.a[0].length-2]]);if(d)for(var d=b.map,f=0,g=e.length;f<g;f++){var h=e[f],l=d.Ga(h),m=b.pixel,c=m[0]-l[0],l=m[1]-l[1],m=a.R&&a.za(b)?1:a.Bj;if(c=Math.sqrt(c*c+l*l)<=m){a.s=h;break}}}return c}
function nu(a,b){var c=b.coordinate;a.s=c;a.c===iu?a.a=c.slice():a.c===hu?(a.a=[[c.slice(),c.slice()]],a.T=a.a[0]):(a.a=[c.slice(),c.slice()],a.c===ou&&(a.T=a.a));a.T&&(a.A=new Am(new P(a.T)));c=a.D(a.a);a.o=new Am;a.tb&&a.o.Cc(a.tb);a.o.Xa(c);qu(a);a.b(new bu("drawstart",a.o))}
function lu(a,b){var c=b.coordinate,d=a.o.Y(),e,f;if(a.c===ju)a.s=c.slice(),f=a.a,f.push(c.slice()),e=f.length>a.Ha,a.D(f,d);else if(a.c===hu){f=a.a[0];f.push(c.slice());if(e=f.length>a.Ha)a.s=f[0];a.D(a.a,d)}qu(a);e&&a.jd()}k.No=function(){var a=this.o.Y(),b,c;this.c===ju?(b=this.a,b.splice(-2,1),this.D(b,a)):this.c===hu&&(b=this.a[0],b.splice(-2,1),c=this.A.Y(),c.sa(b),this.D(this.a,a));0===b.length&&(this.s=null);qu(this)};
k.jd=function(){var a=ru(this),b=this.a,c=a.Y();this.c===ju?(b.pop(),this.D(b,c)):this.c===hu&&(b[0].pop(),b[0].push(b[0][0]),this.D(b,c));"MultiPoint"===this.W?a.Xa(new R([b])):"MultiLineString"===this.W?a.Xa(new Q([b])):"MultiPolygon"===this.W&&a.Xa(new S([b]));this.b(new bu("drawend",a));this.sb&&this.sb.push(a);this.Mb&&this.Mb.ub(a)};function ru(a){a.s=null;var b=a.o;b&&(a.o=null,a.O=null,a.A=null,a.na.ga().clear(!0));return b}
k.mm=function(a){var b=a.Y();this.o=a;this.a=b.Z();a=this.a[this.a.length-1];this.s=a.slice();this.a.push(a.slice());qu(this);this.b(new bu("drawstart",this.o))};k.Ec=oc;function qu(a){var b=[];a.o&&b.push(a.o);a.A&&b.push(a.A);a.O&&b.push(a.O);a=a.na.ga();a.clear(!0);a.Hc(b)}k.vi=function(){var a=this.v,b=this.f();a&&b||ru(this);this.na.setMap(b?a:null)};
function gu(a){var b;"Point"===a||"MultiPoint"===a?b=iu:"LineString"===a||"MultiLineString"===a?b=ju:"Polygon"===a||"MultiPolygon"===a?b=hu:"Circle"===a&&(b=ou);return b}var iu="Point",ju="LineString",hu="Polygon",ou="Circle";function su(a,b,c){Pa.call(this,a);this.features=b;this.mapBrowserEvent=c}w(su,Pa);
function tu(a){Qh.call(this,{handleDownEvent:uu,handleDragEvent:vu,handleEvent:wu,handleUpEvent:xu});this.tb=a.condition?a.condition:Ph;this.Ia=function(a){return Lh(a)&&Kh(a)};this.sb=a.deleteCondition?a.deleteCondition:this.Ia;this.Ha=this.c=null;this.na=[0,0];this.D=this.T=!1;this.a=new Pt;this.O=void 0!==a.pixelTolerance?a.pixelTolerance:10;this.s=this.za=!1;this.o=[];this.R=new F({source:new U({useSpatialIndex:!1,wrapX:!!a.wrapX}),style:a.style?a.style:yu(),updateWhileAnimating:!0,updateWhileInteracting:!0});
this.pa={Point:this.tm,LineString:this.hh,LinearRing:this.hh,Polygon:this.um,MultiPoint:this.rm,MultiLineString:this.qm,MultiPolygon:this.sm,GeometryCollection:this.pm};this.A=a.features;this.A.forEach(this.wf,this);B(this.A,ue,this.nm,this);B(this.A,ve,this.om,this);this.W=null}w(tu,Qh);k=tu.prototype;k.wf=function(a){var b=a.Y();b&&b.X()in this.pa&&this.pa[b.X()].call(this,a,b);(b=this.v)&&zu(this,this.na,b);B(a,"change",this.gh,this)};
function Au(a,b){a.D||(a.D=!0,a.b(new su("modifystart",a.A,b)))}function Bu(a,b){Cu(a,b);a.c&&0===a.A.gc()&&(a.R.ga().pb(a.c),a.c=null);Ka(b,"change",a.gh,a)}function Cu(a,b){var c=a.a,d=[];c.forEach(function(a){b===a.feature&&d.push(a)});for(var e=d.length-1;0<=e;--e)c.remove(d[e])}k.setMap=function(a){this.R.setMap(a);Qh.prototype.setMap.call(this,a)};k.nm=function(a){this.wf(a.element)};k.gh=function(a){this.s||(a=a.target,Bu(this,a),this.wf(a))};k.om=function(a){Bu(this,a.element)};
k.tm=function(a,b){var c=b.Z(),c={feature:a,geometry:b,qa:[c,c]};this.a.Ca(b.C(),c)};k.rm=function(a,b){var c=b.Z(),d,e,f;e=0;for(f=c.length;e<f;++e)d=c[e],d={feature:a,geometry:b,depth:[e],index:e,qa:[d,d]},this.a.Ca(b.C(),d)};k.hh=function(a,b){var c=b.Z(),d,e,f,g;d=0;for(e=c.length-1;d<e;++d)f=c.slice(d,d+2),g={feature:a,geometry:b,index:d,qa:f},this.a.Ca(Db(f),g)};
k.qm=function(a,b){var c=b.Z(),d,e,f,g,h,l,m;g=0;for(h=c.length;g<h;++g)for(d=c[g],e=0,f=d.length-1;e<f;++e)l=d.slice(e,e+2),m={feature:a,geometry:b,depth:[g],index:e,qa:l},this.a.Ca(Db(l),m)};k.um=function(a,b){var c=b.Z(),d,e,f,g,h,l,m;g=0;for(h=c.length;g<h;++g)for(d=c[g],e=0,f=d.length-1;e<f;++e)l=d.slice(e,e+2),m={feature:a,geometry:b,depth:[g],index:e,qa:l},this.a.Ca(Db(l),m)};
k.sm=function(a,b){var c=b.Z(),d,e,f,g,h,l,m,n,p,q;l=0;for(m=c.length;l<m;++l)for(n=c[l],g=0,h=n.length;g<h;++g)for(d=n[g],e=0,f=d.length-1;e<f;++e)p=d.slice(e,e+2),q={feature:a,geometry:b,depth:[g,l],index:e,qa:p},this.a.Ca(Db(p),q)};k.pm=function(a,b){var c,d=b.f;for(c=0;c<d.length;++c)this.pa[d[c].X()].call(this,a,d[c])};function Du(a,b){var c=a.c;c?c.Y().sa(b):(c=new Am(new C(b)),a.c=c,a.R.ga().ub(c))}function Eu(a,b){return a.index-b.index}
function uu(a){if(!this.tb(a))return!1;zu(this,a.pixel,a.map);this.o.length=0;this.D=!1;var b=this.c;if(b){var c=[],b=b.Y().Z(),d=Db([b]),d=St(this.a,d),e={};d.sort(Eu);for(var f=0,g=d.length;f<g;++f){var h=d[f],l=h.qa,m=x(h.feature),n=h.depth;n&&(m+="-"+n.join("-"));e[m]||(e[m]=Array(2));if(yb(l[0],b)&&!e[m][0])this.o.push([h,0]),e[m][0]=h;else if(yb(l[1],b)&&!e[m][1]){if("LineString"!==h.geometry.X()&&"MultiLineString"!==h.geometry.X()||!e[m][0]||0!==e[m][0].index)this.o.push([h,1]),e[m][1]=h}else x(l)in
this.Ha&&!e[m][0]&&!e[m][1]&&c.push([h,b])}c.length&&Au(this,a);for(a=c.length-1;0<=a;--a)this.il.apply(this,c[a])}return!!this.c}
function vu(a){this.T=!1;Au(this,a);a=a.coordinate;for(var b=0,c=this.o.length;b<c;++b){for(var d=this.o[b],e=d[0],f=e.depth,g=e.geometry,h=g.Z(),l=e.qa,d=d[1];a.length<g.ua();)a.push(0);switch(g.X()){case "Point":h=a;l[0]=l[1]=a;break;case "MultiPoint":h[e.index]=a;l[0]=l[1]=a;break;case "LineString":h[e.index+d]=a;l[d]=a;break;case "MultiLineString":h[f[0]][e.index+d]=a;l[d]=a;break;case "Polygon":h[f[0]][e.index+d]=a;l[d]=a;break;case "MultiPolygon":h[f[1]][f[0]][e.index+d]=a,l[d]=a}e=g;this.s=
!0;e.sa(h);this.s=!1}Du(this,a)}function xu(a){for(var b,c=this.o.length-1;0<=c;--c)b=this.o[c][0],Qt(this.a,Db(b.qa),b);this.D&&(this.b(new su("modifyend",this.A,a)),this.D=!1);return!1}function wu(a){if(!(a instanceof Dg))return!0;this.W=a;var b;Fd(a.map.aa())[1]||a.type!=Ng||this.H||(this.na=a.pixel,zu(this,a.pixel,a.map));this.c&&this.sb(a)&&(b=a.type==Ig&&this.T?!0:this.Yh());a.type==Ig&&(this.T=!1);return Rh.call(this,a)&&!b}
function zu(a,b,c){function d(a,b){return Bb(e,a.qa)-Bb(e,b.qa)}var e=c.Na(b),f=c.Na([b[0]-a.O,b[1]+a.O]),g=c.Na([b[0]+a.O,b[1]-a.O]),f=Db([f,g]),f=St(a.a,f);if(0<f.length){f.sort(d);var g=f[0].qa,h=vb(e,g),l=c.Ga(h);if(Math.sqrt(Ab(b,l))<=a.O){b=c.Ga(g[0]);c=c.Ga(g[1]);b=Ab(l,b);c=Ab(l,c);a.za=Math.sqrt(Math.min(b,c))<=a.O;a.za&&(h=b>c?g[1]:g[0]);Du(a,h);c={};c[x(g)]=!0;b=1;for(l=f.length;b<l;++b)if(h=f[b].qa,yb(g[0],h[0])&&yb(g[1],h[1])||yb(g[0],h[1])&&yb(g[1],h[0]))c[x(h)]=!0;else break;a.Ha=c;
return}}a.c&&(a.R.ga().pb(a.c),a.c=null)}
k.il=function(a,b){for(var c=a.qa,d=a.feature,e=a.geometry,f=a.depth,g=a.index,h;b.length<e.ua();)b.push(0);switch(e.X()){case "MultiLineString":h=e.Z();h[f[0]].splice(g+1,0,b);break;case "Polygon":h=e.Z();h[f[0]].splice(g+1,0,b);break;case "MultiPolygon":h=e.Z();h[f[1]][f[0]].splice(g+1,0,b);break;case "LineString":h=e.Z();h.splice(g+1,0,b);break;default:return}this.s=!0;e.sa(h);this.s=!1;h=this.a;h.remove(a);Fu(this,e,g,f,1);var l={qa:[c[0],b],feature:d,geometry:e,depth:f,index:g};h.Ca(Db(l.qa),
l);this.o.push([l,1]);c={qa:[b,c[1]],feature:d,geometry:e,depth:f,index:g+1};h.Ca(Db(c.qa),c);this.o.push([c,0]);this.T=!0};
k.Yh=function(){var a=!1;if(this.W&&this.W.type!=Og){var b=this.W;Au(this,b);var c=this.o,a={},d=!1,e,f,g,h,l,m,n,p;for(h=c.length-1;0<=h;--h)g=c[h],n=g[0],p=x(n.feature),n.depth&&(p+="-"+n.depth.join("-")),p in a||(a[p]={}),0===g[1]?(a[p].right=n,a[p].index=n.index):1==g[1]&&(a[p].left=n,a[p].index=n.index+1);for(p in a){m=a[p].right;h=a[p].left;g=a[p].index;l=g-1;n=void 0!==h?h:m;0>l&&(l=0);c=n.geometry;e=f=c.Z();d=!1;switch(c.X()){case "MultiLineString":2<f[n.depth[0]].length&&(f[n.depth[0]].splice(g,
1),d=!0);break;case "LineString":2<f.length&&(f.splice(g,1),d=!0);break;case "MultiPolygon":e=e[n.depth[1]];case "Polygon":e=e[n.depth[0]],4<e.length&&(g==e.length-1&&(g=0),e.splice(g,1),d=!0,0===g&&(e.pop(),e.push(e[0]),l=e.length-1))}d&&(e=c,this.s=!0,e.sa(f),this.s=!1,f=[],void 0!==h&&(this.a.remove(h),f.push(h.qa[0])),void 0!==m&&(this.a.remove(m),f.push(m.qa[1])),void 0!==h&&void 0!==m&&(h={depth:n.depth,feature:n.feature,geometry:n.geometry,index:l,qa:f},this.a.Ca(Db(h.qa),h)),Fu(this,c,g,n.depth,
-1),this.c&&(this.R.ga().pb(this.c),this.c=null))}a=d;this.b(new su("modifyend",this.A,b));this.D=!1}return a};function Fu(a,b,c,d,e){Ut(a.a,b.C(),function(a){a.geometry===b&&(void 0===d||void 0===a.depth||hb(a.depth,d))&&a.index>c&&(a.index+=e)})}function yu(){var a=dj();return function(){return a.Point}};function Gu(a,b,c,d){Pa.call(this,a);this.selected=b;this.deselected=c;this.mapBrowserEvent=d}w(Gu,Pa);
function Hu(a){Bh.call(this,{handleEvent:Iu});a=a?a:{};this.H=a.condition?a.condition:Kh;this.A=a.addCondition?a.addCondition:oc;this.D=a.removeCondition?a.removeCondition:oc;this.O=a.toggleCondition?a.toggleCondition:Mh;this.o=a.multi?a.multi:!1;this.j=a.filter?a.filter:nc;this.c=new F({source:new U({useSpatialIndex:!1,features:a.features,wrapX:a.wrapX}),style:a.style?a.style:Ju(),updateWhileAnimating:!0,updateWhileInteracting:!0});if(a.layers)if("function"===typeof a.layers)a=a.layers;else{var b=
a.layers;a=function(a){return bb(b,a)}}else a=nc;this.s=a;this.a={};a=this.c.ga().c;B(a,ue,this.vm,this);B(a,ve,this.ym,this)}w(Hu,Bh);k=Hu.prototype;k.wm=function(){return this.c.ga().c};k.xm=function(a){la(a instanceof Am,42);a=x(a);return this.a[a]};
function Iu(a){if(!this.H(a))return!0;var b=this.A(a),c=this.D(a),d=this.O(a),e=!b&&!c&&!d,f=a.map,g=this.c.ga().c,h=[],l=[];if(e)Aa(this.a),f.Rd(a.pixel,function(a,b){if(this.j(a,b)){l.push(a);var c=x(a);this.a[c]=b;return!this.o}},this,this.s),0<l.length&&1==g.gc()&&g.item(0)==l[0]?l.length=0:(0!==g.gc()&&(h=Array.prototype.concat(g.a),g.clear()),g.pf(l));else{f.Rd(a.pixel,function(a,e){if(this.j(a,e)){if(!b&&!d||bb(g.a,a))(c||d)&&bb(g.a,a)&&(h.push(a),f=x(a),delete this.a[f]);else{l.push(a);var f=
x(a);this.a[f]=e}return!this.o}},this,this.s);for(e=h.length-1;0<=e;--e)g.remove(h[e]);g.pf(l)}(0<l.length||0<h.length)&&this.b(new Gu("select",l,h,a));return Jh(a)}k.setMap=function(a){var b=this.v,c=this.c.ga().c;b&&c.forEach(b.ti,b);Bh.prototype.setMap.call(this,a);this.c.setMap(a);a&&c.forEach(a.pi,a)};function Ju(){var a=dj();eb(a.Polygon,a.LineString);eb(a.GeometryCollection,a.LineString);return function(b){return a[b.Y().X()]}}k.vm=function(a){var b=this.v;b&&b.pi(a.element)};
k.ym=function(a){var b=this.v;b&&b.ti(a.element)};function Ku(a){Qh.call(this,{handleEvent:Lu,handleDownEvent:nc,handleUpEvent:Mu});a=a?a:{};this.s=a.source?a.source:null;this.na=void 0!==a.vertex?a.vertex:!0;this.T=void 0!==a.edge?a.edge:!0;this.o=a.features?a.features:null;this.za=[];this.D={};this.O={};this.W={};this.A={};this.R=null;this.c=void 0!==a.pixelTolerance?a.pixelTolerance:10;this.Ha=Nu.bind(this);this.a=new Pt;this.pa={Point:this.Em,LineString:this.kh,LinearRing:this.kh,Polygon:this.Fm,MultiPoint:this.Cm,MultiLineString:this.Bm,MultiPolygon:this.Dm,
GeometryCollection:this.Am}}w(Ku,Qh);k=Ku.prototype;k.ub=function(a,b){var c=void 0!==b?b:!0,d=x(a),e=a.Y();if(e){var f=this.pa[e.X()];f&&(this.W[d]=e.C(Eb()),f.call(this,a,e),c&&(this.O[d]=B(e,"change",this.Hk.bind(this,a),this)))}c&&(this.D[d]=B(a,Za(a.a),this.zm,this))};k.Gj=function(a){this.ub(a)};k.Hj=function(a){this.pb(a)};k.ih=function(a){var b;a instanceof Zt?b=a.feature:a instanceof te&&(b=a.element);this.ub(b)};
k.jh=function(a){var b;a instanceof Zt?b=a.feature:a instanceof te&&(b=a.element);this.pb(b)};k.zm=function(a){a=a.target;this.pb(a,!0);this.ub(a,!0)};k.Hk=function(a){if(this.H){var b=x(a);b in this.A||(this.A[b]=a)}else this.ui(a)};k.pb=function(a,b){var c=void 0!==b?b:!0,d=x(a),e=this.W[d];if(e){var f=this.a,g=[];Ut(f,e,function(b){a===b.feature&&g.push(b)});for(e=g.length-1;0<=e;--e)f.remove(g[e]);c&&(Va(this.O[d]),delete this.O[d])}c&&(Va(this.D[d]),delete this.D[d])};
k.setMap=function(a){var b=this.v,c=this.za,d;this.o?d=this.o:this.s&&(d=this.s.me());b&&(c.forEach(Va),c.length=0,d.forEach(this.Hj,this));Qh.prototype.setMap.call(this,a);a&&(this.o?c.push(B(this.o,ue,this.ih,this),B(this.o,ve,this.jh,this)):this.s&&c.push(B(this.s,"addfeature",this.ih,this),B(this.s,"removefeature",this.jh,this)),d.forEach(this.Gj,this))};k.Ec=oc;k.ui=function(a){this.pb(a,!1);this.ub(a,!1)};k.Am=function(a,b){var c,d=b.f;for(c=0;c<d.length;++c)this.pa[d[c].X()].call(this,a,d[c])};
k.kh=function(a,b){var c=b.Z(),d,e,f,g;d=0;for(e=c.length-1;d<e;++d)f=c.slice(d,d+2),g={feature:a,qa:f},this.a.Ca(Db(f),g)};k.Bm=function(a,b){var c=b.Z(),d,e,f,g,h,l,m;g=0;for(h=c.length;g<h;++g)for(d=c[g],e=0,f=d.length-1;e<f;++e)l=d.slice(e,e+2),m={feature:a,qa:l},this.a.Ca(Db(l),m)};k.Cm=function(a,b){var c=b.Z(),d,e,f;e=0;for(f=c.length;e<f;++e)d=c[e],d={feature:a,qa:[d,d]},this.a.Ca(b.C(),d)};
k.Dm=function(a,b){var c=b.Z(),d,e,f,g,h,l,m,n,p,q;l=0;for(m=c.length;l<m;++l)for(n=c[l],g=0,h=n.length;g<h;++g)for(d=n[g],e=0,f=d.length-1;e<f;++e)p=d.slice(e,e+2),q={feature:a,qa:p},this.a.Ca(Db(p),q)};k.Em=function(a,b){var c=b.Z(),c={feature:a,qa:[c,c]};this.a.Ca(b.C(),c)};k.Fm=function(a,b){var c=b.Z(),d,e,f,g,h,l,m;g=0;for(h=c.length;g<h;++g)for(d=c[g],e=0,f=d.length-1;e<f;++e)l=d.slice(e,e+2),m={feature:a,qa:l},this.a.Ca(Db(l),m)};
function Lu(a){var b,c,d=a.pixel,e=a.coordinate;b=a.map;var f=b.Na([d[0]-this.c,d[1]+this.c]);c=b.Na([d[0]+this.c,d[1]-this.c]);var f=Db([f,c]),g=St(this.a,f),h,f=!1,l=null;c=null;if(0<g.length){this.R=e;g.sort(this.Ha);g=g[0].qa;if(this.na&&!this.T){if(e=b.Ga(g[0]),h=b.Ga(g[1]),e=Ab(d,e),d=Ab(d,h),h=Math.sqrt(Math.min(e,d)),h=h<=this.c)f=!0,l=e>d?g[1]:g[0],c=b.Ga(l)}else this.T&&(l=vb(e,g),c=b.Ga(l),Math.sqrt(Ab(d,c))<=this.c&&(f=!0,this.na&&(e=b.Ga(g[0]),h=b.Ga(g[1]),e=Ab(c,e),d=Ab(c,h),h=Math.sqrt(Math.min(e,
d)),h=h<=this.c)))&&(l=e>d?g[1]:g[0],c=b.Ga(l));f&&(c=[Math.round(c[0]),Math.round(c[1])])}b=l;f&&(a.coordinate=b.slice(0,2),a.pixel=c);return Rh.call(this,a)}function Mu(){var a=Ba(this.A);a.length&&(a.forEach(this.ui,this),this.A={});return!1}function Nu(a,b){return Bb(this.R,a.qa)-Bb(this.R,b.qa)};function Ou(a,b,c){Pa.call(this,a);this.features=b;this.coordinate=c}w(Ou,Pa);function Pu(a){Qh.call(this,{handleDownEvent:Qu,handleDragEvent:Ru,handleMoveEvent:Su,handleUpEvent:Tu});this.s=void 0;this.a=null;this.c=void 0!==a.features?a.features:null;if(a.layers)if("function"===typeof a.layers)a=a.layers;else{var b=a.layers;a=function(a){return bb(b,a)}}else a=nc;this.A=a;this.o=null}w(Pu,Qh);
function Qu(a){this.o=Uu(this,a.pixel,a.map);return!this.a&&this.o?(this.a=a.coordinate,Su.call(this,a),this.b(new Ou("translatestart",this.c,a.coordinate)),!0):!1}function Tu(a){return this.a?(this.a=null,Su.call(this,a),this.b(new Ou("translateend",this.c,a.coordinate)),!0):!1}
function Ru(a){if(this.a){a=a.coordinate;var b=a[0]-this.a[0],c=a[1]-this.a[1];if(this.c)this.c.forEach(function(a){var d=a.Y();d.Qc(b,c);a.Xa(d)});else if(this.o){var d=this.o.Y();d.Qc(b,c);this.o.Xa(d)}this.a=a;this.b(new Ou("translating",this.c,a))}}function Su(a){var b=a.map.xc();Uu(this,a.pixel,a.map)?(this.s=b.style.cursor,b.style.cursor=this.a?"-webkit-grabbing":"-webkit-grab",b.style.cursor=this.a?"grabbing":"grab"):(b.style.cursor=void 0!==this.s?this.s:"",this.s=void 0)}
function Uu(a,b,c){var d=null;b=c.Rd(b,function(a){return a},a,a.A);a.c&&bb(a.c.a,b)&&(d=b);return d};function V(a){a=a?a:{};var b=za({},a);delete b.gradient;delete b.radius;delete b.blur;delete b.shadow;delete b.weight;F.call(this,b);this.f=null;this.ha=void 0!==a.shadow?a.shadow:250;this.W=void 0;this.c=null;B(this,Za("gradient"),this.Ik,this);this.fi(a.gradient?a.gradient:Vu);this.ai(void 0!==a.blur?a.blur:15);this.nh(void 0!==a.radius?a.radius:8);B(this,Za("blur"),this.kf,this);B(this,Za("radius"),this.kf,this);this.kf();var c=a.weight?a.weight:"weight",d;"string"===typeof c?d=function(a){return a.get(c)}:
d=c;this.l(function(a){a=d(a);a=void 0!==a?ma(a,0,1):1;var b=255*a|0,c=this.c[b];c||(c=[new Zi({image:new Qo({opacity:a,src:this.W})})],this.c[b]=c);return c}.bind(this));this.set("renderOrder",null);B(this,"render",this.Zk,this)}w(V,F);var Vu=["#00f","#0ff","#0f0","#ff0","#f00"];k=V.prototype;k.xg=function(){return this.get("blur")};k.Eg=function(){return this.get("gradient")};k.mh=function(){return this.get("radius")};
k.Ik=function(){for(var a=this.Eg(),b=$e(1,256),c=b.createLinearGradient(0,0,1,256),d=1/(a.length-1),e=0,f=a.length;e<f;++e)c.addColorStop(e*d,a[e]);b.fillStyle=c;b.fillRect(0,0,1,256);this.f=b.getImageData(0,0,1,256).data};k.kf=function(){var a=this.mh(),b=this.xg(),c=a+b+1,d=2*c,d=$e(d,d);d.shadowOffsetX=d.shadowOffsetY=this.ha;d.shadowBlur=b;d.shadowColor="#000";d.beginPath();b=c-this.ha;d.arc(b,b,a,0,2*Math.PI,!0);d.fill();this.W=d.canvas.toDataURL();this.c=Array(256);this.u()};
k.Zk=function(a){a=a.context;var b=a.canvas,b=a.getImageData(0,0,b.width,b.height),c=b.data,d,e,f;d=0;for(e=c.length;d<e;d+=4)if(f=4*c[d+3])c[d]=this.f[f],c[d+1]=this.f[f+1],c[d+2]=this.f[f+2];a.putImageData(b,0,0)};k.ai=function(a){this.set("blur",a)};k.fi=function(a){this.set("gradient",a)};k.nh=function(a){this.set("radius",a)};function Wu(a,b,c,d){function e(){delete ja[g];f.parentNode.removeChild(f)}var f=ja.document.createElement("script"),g="olc_"+x(b);f.async=!0;f.src=a+(-1==a.indexOf("?")?"?":"&")+(d||"callback")+"="+g;var h=ja.setTimeout(function(){e();c&&c()},1E4);ja[g]=function(a){ja.clearTimeout(h);e();b(a)};ja.document.getElementsByTagName("head")[0].appendChild(f)};function Xu(a,b,c,d,e,f,g,h,l,m,n){qh.call(this,e,0);this.O=void 0!==n?n:!1;this.D=g;this.H=h;this.l=null;this.c={};this.o=b;this.v=d;this.U=f?f:e;this.g=[];this.Vc=null;this.s=0;f=d.Ea(this.U);h=this.v.C();e=this.o.C();f=h?jc(f,h):f;if(0===cc(f))this.state=4;else if((h=a.C())&&(e?e=jc(e,h):e=h),d=d.$(this.U[0]),d=mk(a,c,gc(f),d),!isFinite(d)||0>=d)this.state=4;else if(this.A=new pk(a,c,f,e,d*(void 0!==m?m:.5)),0===this.A.f.length)this.state=4;else if(this.s=b.ec(d),c=rk(this.A),e&&(a.a?(c[1]=ma(c[1],
e[1],e[3]),c[3]=ma(c[3],e[1],e[3])):c=jc(c,e)),cc(c)){a=ge(b,c,this.s);for(b=a.ca;b<=a.ea;b++)for(c=a.fa;c<=a.ia;c++)(m=l(this.s,b,c,g))&&this.g.push(m);0===this.g.length&&(this.state=4)}else this.state=4}w(Xu,qh);Xu.prototype.ma=function(){1==this.state&&(this.Vc.forEach(Ea),this.Vc=null);qh.prototype.ma.call(this)};Xu.prototype.ab=function(a){if(void 0!==a){var b=x(a);if(b in this.c)return this.c[b];a=Ca(this.c)?this.l:this.l.cloneNode(!1);return this.c[b]=a}return this.l};
Xu.prototype.yd=function(){var a=[];this.g.forEach(function(b){b&&2==b.V()&&a.push({extent:this.o.Ea(b.oa),image:b.ab()})},this);this.g.length=0;if(0===a.length)this.state=3;else{var b=this.U[0],c=this.v.Qa(b),d="number"===typeof c?c:c[0],c="number"===typeof c?c:c[1],b=this.v.$(b),e=this.o.$(this.s),f=this.v.Ea(this.U);this.l=ok(d,c,this.D,e,this.o.C(),b,f,this.A,a,this.H,this.O);this.state=2}rh(this)};
Xu.prototype.load=function(){if(0==this.state){this.state=1;rh(this);var a=0;this.Vc=[];this.g.forEach(function(b){var c=b.V();if(0==c||1==c){a++;var d;d=B(b,"change",function(){var c=b.V();if(2==c||3==c||4==c)Ea(d),a--,0===a&&(this.Vc.forEach(Ea),this.Vc=null,this.yd())},this);this.Vc.push(d)}},this);this.g.forEach(function(a){0==a.V()&&a.load()});0===a&&ja.setTimeout(this.yd.bind(this),0)}};function Yu(a,b){var c=/\{z\}/g,d=/\{x\}/g,e=/\{y\}/g,f=/\{-y\}/g;return function(g){if(g)return a.replace(c,g[0].toString()).replace(d,g[1].toString()).replace(e,function(){return(-g[2]-1).toString()}).replace(f,function(){var a=b.a?b.a[g[0]]:null;la(a,55);return(a.ia-a.fa+1+g[2]).toString()})}}function Zu(a,b){for(var c=a.length,d=Array(c),e=0;e<c;++e)d[e]=Yu(a[e],b);return $u(d)}function $u(a){return 1===a.length?a[0]:function(b,c,d){if(b)return a[ta((b[1]<<b[0])+b[2],a.length)](b,c,d)}}
function av(){}function bv(a){var b=[],c=/\{(\d)-(\d)\}/.exec(a)||/\{([a-z])-([a-z])\}/.exec(a);if(c){var d=c[2].charCodeAt(0),e;for(e=c[1].charCodeAt(0);e<=d;++e)b.push(a.replace(c[0],String.fromCharCode(e)))}else b.push(a);return b};function cv(a){Vl.call(this);this.c=void 0!==a?a:2048}w(cv,Vl);function dv(a){return a.f>a.c}cv.prototype.Jc=function(a){for(var b,c;dv(this);){b=this.a.Fc;c=b.oa[0].toString();var d;if(d=c in a)b=b.oa,d=Wd(a[c],b[1],b[2]);if(d)break;else Oa(this.pop())}};function ev(a){Tg.call(this,{attributions:a.attributions,extent:a.extent,logo:a.logo,projection:a.projection,state:a.state,wrapX:a.wrapX});this.ha=void 0!==a.opaque?a.opaque:!1;this.na=void 0!==a.tilePixelRatio?a.tilePixelRatio:1;this.tileGrid=void 0!==a.tileGrid?a.tileGrid:null;this.a=new cv(a.cacheSize);this.j=[0,0];this.fc=""}w(ev,Tg);k=ev.prototype;k.xh=function(){return dv(this.a)};k.Jc=function(a,b){var c=this.od(a);c&&c.Jc(b)};
function uj(a,b,c,d,e){b=a.od(b);if(!b)return!1;for(var f=!0,g,h,l=d.ca;l<=d.ea;++l)for(var m=d.fa;m<=d.ia;++m)g=a.Ib(c,l,m),h=!1,b.b.hasOwnProperty(g)&&(g=b.get(g),(h=2===g.V())&&(h=!1!==e(g))),h||(f=!1);return f}k.Ud=function(){return 0};function fv(a,b){a.fc!==b&&(a.fc=b,a.u())}k.Ib=function(a,b,c){return a+"/"+b+"/"+c};k.gf=function(){return this.ha};k.Pa=function(){return this.tileGrid};k.fb=function(a){return this.tileGrid?this.tileGrid:me(a)};
k.od=function(a){var b=this.f;return b&&!Lc(b,a)?null:this.a};k.zb=function(){return this.na};k.jf=function(a,b,c){c=this.fb(c);b=this.zb(b);a=ae(c.Qa(a),this.j);return 1==b?a:$d(a,b,this.j)};function gv(a,b,c){var d=void 0!==c?c:a.f;c=a.fb(d);if(a.D&&d.g){var e=b;b=e[0];a=le(c,e);d=ne(d);Lb(d,a)?b=e:(e=ec(d),a[0]+=e*Math.ceil((d[0]-a[0])/e),b=c.pd(a,b))}e=b[0];d=b[1];a=b[2];if(c.minZoom>e||e>c.maxZoom)c=!1;else{var f=c.C();c=(c=f?ge(c,f,e):c.a?c.a[e]:null)?Wd(c,d,a):!0}return c?b:null}
k.va=function(){this.a.clear();this.u()};k.Xf=ha;function hv(a,b){Pa.call(this,a);this.tile=b}w(hv,Pa);function iv(a){ev.call(this,{attributions:a.attributions,cacheSize:a.cacheSize,extent:a.extent,logo:a.logo,opaque:a.opaque,projection:a.projection,state:a.state,tileGrid:a.tileGrid,tilePixelRatio:a.tilePixelRatio,wrapX:a.wrapX});this.tileLoadFunction=a.tileLoadFunction;this.tileUrlFunction=this.vc?this.vc.bind(this):av;this.urls=null;a.urls?this.Ua(a.urls):a.url&&this.Za(a.url);a.tileUrlFunction&&this.Ta(a.tileUrlFunction)}w(iv,ev);k=iv.prototype;k.gb=function(){return this.tileLoadFunction};
k.hb=function(){return this.tileUrlFunction};k.ib=function(){return this.urls};k.yh=function(a){a=a.target;switch(a.V()){case 1:this.b(new hv("tileloadstart",a));break;case 2:this.b(new hv("tileloadend",a));break;case 3:this.b(new hv("tileloaderror",a))}};k.mb=function(a){this.a.clear();this.tileLoadFunction=a;this.u()};k.Ta=function(a,b){this.tileUrlFunction=a;"undefined"!==typeof b?fv(this,b):this.u()};
k.Za=function(a){var b=this.urls=bv(a);this.Ta(this.vc?this.vc.bind(this):Zu(b,this.tileGrid),a)};k.Ua=function(a){this.urls=a;var b=a.join("\n");this.Ta(this.vc?this.vc.bind(this):Zu(a,this.tileGrid),b)};k.Xf=function(a,b,c){a=this.Ib(a,b,c);this.a.b.hasOwnProperty(a)&&this.a.get(a)};function W(a){iv.call(this,{attributions:a.attributions,cacheSize:a.cacheSize,extent:a.extent,logo:a.logo,opaque:a.opaque,projection:a.projection,state:a.state,tileGrid:a.tileGrid,tileLoadFunction:a.tileLoadFunction?a.tileLoadFunction:jv,tilePixelRatio:a.tilePixelRatio,tileUrlFunction:a.tileUrlFunction,url:a.url,urls:a.urls,wrapX:a.wrapX});this.crossOrigin=void 0!==a.crossOrigin?a.crossOrigin:null;this.tileClass=void 0!==a.tileClass?a.tileClass:Dt;this.i={};this.s={};this.pa=a.reprojectionErrorThreshold;
this.H=!1}w(W,iv);k=W.prototype;k.xh=function(){if(dv(this.a))return!0;for(var a in this.i)if(dv(this.i[a]))return!0;return!1};k.Jc=function(a,b){var c=this.od(a);this.a.Jc(this.a==c?b:{});for(var d in this.i){var e=this.i[d];e.Jc(e==c?b:{})}};k.Ud=function(a){return this.f&&a&&!Lc(this.f,a)?0:this.ef()};k.ef=function(){return 0};k.gf=function(a){return this.f&&a&&!Lc(this.f,a)?!1:iv.prototype.gf.call(this,a)};
k.fb=function(a){var b=this.f;return!this.tileGrid||b&&!Lc(b,a)?(b=x(a).toString(),b in this.s||(this.s[b]=me(a)),this.s[b]):this.tileGrid};k.od=function(a){var b=this.f;if(!b||Lc(b,a))return this.a;a=x(a).toString();a in this.i||(this.i[a]=new cv);return this.i[a]};function kv(a,b,c,d,e,f,g){b=[b,c,d];e=(c=gv(a,b,f))?a.tileUrlFunction(c,e,f):void 0;e=new a.tileClass(b,void 0!==e?0:4,void 0!==e?e:"",a.crossOrigin,a.tileLoadFunction);e.key=g;B(e,"change",a.yh,a);return e}
k.dc=function(a,b,c,d,e){if(this.f&&e&&!Lc(this.f,e)){var f=this.od(e);c=[a,b,c];var g;a=this.Ib.apply(this,c);f.b.hasOwnProperty(a)&&(g=f.get(a));b=this.fc;if(g&&g.key==b)return g;var h=this.f,l=this.fb(h),m=this.fb(e),n=gv(this,c,e);d=new Xu(h,l,e,m,c,n,this.zb(d),this.ef(),function(a,b,c,d){return lv(this,a,b,c,d,h)}.bind(this),this.pa,this.H);d.key=b;g?(d.a=g,f.replace(a,d)):f.set(a,d);return d}return lv(this,a,b,c,d,e)};
function lv(a,b,c,d,e,f){var g,h=a.Ib(b,c,d),l=a.fc;if(a.a.b.hasOwnProperty(h)){if(g=a.a.get(h),g.key!=l){var m=g;g.a&&g.a.key==l?(g=g.a,2==m.V()&&(g.a=m)):(g=kv(a,b,c,d,e,f,l),2==m.V()?g.a=m:m.a&&2==m.a.V()&&(g.a=m.a,m.a=null));g.a&&(g.a.a=null);a.a.replace(h,g)}}else g=kv(a,b,c,d,e,f,l),a.a.set(h,g);return g}k.Db=function(a){if(this.H!=a){this.H=a;for(var b in this.i)this.i[b].clear();this.u()}};k.Eb=function(a,b){var c=vc(a);c&&(c=x(c).toString(),c in this.s||(this.s[c]=b))};
function jv(a,b){a.ab().src=b};function mv(a){W.call(this,{cacheSize:a.cacheSize,crossOrigin:"anonymous",opaque:!0,projection:vc("EPSG:3857"),reprojectionErrorThreshold:a.reprojectionErrorThreshold,state:"loading",tileLoadFunction:a.tileLoadFunction,wrapX:void 0!==a.wrapX?a.wrapX:!0});this.o=void 0!==a.culture?a.culture:"en-us";this.c=void 0!==a.maxZoom?a.maxZoom:-1;Wu("https://dev.virtualearth.net/REST/v1/Imagery/Metadata/"+a.imagerySet+"?uriScheme=https&include=ImageryProviders&key="+a.key,this.v.bind(this),void 0,"jsonp")}
w(mv,W);var nv=new qe({html:'<a class="ol-attribution-bing-tos" href="http://www.microsoft.com/maps/product/terms.html">Terms of Use</a>'});
mv.prototype.v=function(a){if(200!=a.statusCode||"OK"!=a.statusDescription||"ValidCredentials"!=a.authenticationResultCode||1!=a.resourceSets.length||1!=a.resourceSets[0].resources.length)Vg(this,"error");else{var b=a.brandLogoUri;-1==b.indexOf("https")&&(b=b.replace("http","https"));var c=a.resourceSets[0].resources[0],d=-1==this.c?c.zoomMax:this.c;a=ne(this.f);var e=pe({extent:a,minZoom:c.zoomMin,maxZoom:d,tileSize:c.imageWidth==c.imageHeight?c.imageWidth:[c.imageWidth,c.imageHeight]});this.tileGrid=
e;var f=this.o;this.tileUrlFunction=$u(c.imageUrlSubdomains.map(function(a){var b=[0,0,0],d=c.imageUrl.replace("{subdomain}",a).replace("{culture}",f);return function(a){if(a)return be(a[0],a[1],-a[2]-1,b),d.replace("{quadkey}",ce(b))}}));if(c.imageryProviders){var g=yc(vc("EPSG:4326"),this.f);a=c.imageryProviders.map(function(a){var b=a.attribution,c={};a.coverageAreas.forEach(function(a){var b=a.zoomMin,f=Math.min(a.zoomMax,d);a=a.bbox;a=mc([a[1],a[0],a[3],a[2]],g);var h,l;for(h=b;h<=f;++h)l=h.toString(),
b=ge(e,a,h),l in c?c[l].push(b):c[l]=[b]});return new qe({html:b,tileRanges:c})});a.push(nv);this.ra(a)}this.O=b;Vg(this,"ready")}};function ov(a){a=a||{};var b=void 0!==a.projection?a.projection:"EPSG:3857",c=void 0!==a.tileGrid?a.tileGrid:pe({extent:ne(b),maxZoom:a.maxZoom,minZoom:a.minZoom,tileSize:a.tileSize});W.call(this,{attributions:a.attributions,cacheSize:a.cacheSize,crossOrigin:a.crossOrigin,logo:a.logo,opaque:a.opaque,projection:b,reprojectionErrorThreshold:a.reprojectionErrorThreshold,tileGrid:c,tileLoadFunction:a.tileLoadFunction,tilePixelRatio:a.tilePixelRatio,tileUrlFunction:a.tileUrlFunction,url:a.url,urls:a.urls,
wrapX:void 0!==a.wrapX?a.wrapX:!0})}w(ov,W);function pv(a){this.v=a.account;this.A=a.map||"";this.c=a.config||{};this.o={};ov.call(this,{attributions:a.attributions,cacheSize:a.cacheSize,crossOrigin:a.crossOrigin,logo:a.logo,maxZoom:void 0!==a.maxZoom?a.maxZoom:18,minZoom:a.minZoom,projection:a.projection,state:"loading",wrapX:a.wrapX});qv(this)}w(pv,ov);k=pv.prototype;k.Qj=function(){return this.c};k.rp=function(a){za(this.c,a);qv(this)};k.Xo=function(a){this.c=a||{};qv(this)};
function qv(a){var b=JSON.stringify(a.c);if(a.o[b])rv(a,a.o[b]);else{var c="https://"+a.v+".cartodb.com/api/v1/map";a.A&&(c+="/named/"+a.A);var d=new XMLHttpRequest;d.addEventListener("load",a.Kk.bind(a,b));d.addEventListener("error",a.Jk.bind(a));d.open("POST",c);d.setRequestHeader("Content-type","application/json");d.send(JSON.stringify(a.c))}}
k.Kk=function(a,b){var c=b.target;if(!c.status||200<=c.status&&300>c.status){var d;try{d=JSON.parse(c.responseText)}catch(e){Vg(this,"error");return}rv(this,d);this.o[a]=d;Vg(this,"ready")}else Vg(this,"error")};k.Jk=function(){Vg(this,"error")};function rv(a,b){a.Za("https://"+b.cdn_url.https+"/"+a.v+"/api/v1/map/"+b.layergroupid+"/{z}/{x}/{y}.png")};function Y(a){U.call(this,{attributions:a.attributions,extent:a.extent,logo:a.logo,projection:a.projection,wrapX:a.wrapX});this.H=void 0;this.pa=void 0!==a.distance?a.distance:20;this.A=[];this.za=a.geometryFunction||function(a){a=a.Y();la(a instanceof C,10);return a};this.v=a.source;this.v.I("change",Y.prototype.Ia,this)}w(Y,U);Y.prototype.sb=function(){return this.v};Y.prototype.Nc=function(a,b,c){this.v.Nc(a,b,c);b!==this.H&&(this.clear(),this.H=b,sv(this),this.Hc(this.A))};
Y.prototype.tb=function(a){this.pa=a;this.Ia()};Y.prototype.Ia=function(){this.clear();sv(this);this.Hc(this.A);this.u()};function sv(a){if(void 0!==a.H){a.A.length=0;for(var b=Eb(),c=a.pa*a.H,d=a.v.me(),e={},f=0,g=d.length;f<g;f++){var h=d[f];x(h).toString()in e||!(h=a.za(h))||(h=h.Z(),Rb(h,b),Gb(b,c,b),h=a.v.cf(b),h=h.filter(function(a){a=x(a).toString();return a in e?!1:e[a]=!0}),a.A.push(tv(a,h)))}}}
function tv(a,b){for(var c=[0,0],d=b.length-1;0<=d;--d){var e=a.za(b[d]);e?ub(c,e.Z()):b.splice(d,1)}d=1/b.length;c[0]*=d;c[1]*=d;c=new Am(new C(c));c.set("features",b);return c};function uv(a,b){var c=[];Object.keys(b).forEach(function(a){null!==b[a]&&void 0!==b[a]&&c.push(a+"="+encodeURIComponent(b[a]))});var d=c.join("&");a=a.replace(/[?&]$/,"");a=-1===a.indexOf("?")?a+"?":a+"&";return a+d};function vv(a){a=a||{};tk.call(this,{attributions:a.attributions,logo:a.logo,projection:a.projection,resolutions:a.resolutions});this.W=void 0!==a.crossOrigin?a.crossOrigin:null;this.i=a.url;this.j=void 0!==a.imageLoadFunction?a.imageLoadFunction:zk;this.v=a.params||{};this.c=null;this.s=[0,0];this.T=0;this.R=void 0!==a.ratio?a.ratio:1.5}w(vv,tk);k=vv.prototype;k.Nm=function(){return this.v};
k.Kc=function(a,b,c,d){if(void 0===this.i)return null;b=uk(this,b);var e=this.c;if(e&&this.T==this.g&&e.$()==b&&e.f==c&&Nb(e.C(),a))return e;e={F:"image",FORMAT:"PNG32",TRANSPARENT:!0};za(e,this.v);a=a.slice();var f=(a[0]+a[2])/2,g=(a[1]+a[3])/2;if(1!=this.R){var h=this.R*ec(a)/2,l=this.R*fc(a)/2;a[0]=f-h;a[1]=g-l;a[2]=f+h;a[3]=g+l}var h=b/c,l=Math.ceil(ec(a)/h),m=Math.ceil(fc(a)/h);a[0]=f-h*l/2;a[2]=f+h*l/2;a[1]=g-h*m/2;a[3]=g+h*m/2;this.s[0]=l;this.s[1]=m;f=a;g=this.s;d=d.eb.split(":").pop();e.SIZE=
g[0]+","+g[1];e.BBOX=f.join(",");e.BBOXSR=d;e.IMAGESR=d;e.DPI=90*c;d=this.i;f=d.replace(/MapServer\/?$/,"MapServer/export").replace(/ImageServer\/?$/,"ImageServer/exportImage");f==d&&la(!1,50);e=uv(f,e);this.c=new Ct(a,b,c,this.l,e,this.W,this.j);this.T=this.g;B(this.c,"change",this.o,this);return this.c};k.Mm=function(){return this.j};k.Om=function(){return this.i};k.Pm=function(a){this.c=null;this.j=a;this.u()};k.Qm=function(a){a!=this.i&&(this.i=a,this.c=null,this.u())};
k.Rm=function(a){za(this.v,a);this.c=null;this.u()};function wv(a){tk.call(this,{projection:a.projection,resolutions:a.resolutions});this.W=void 0!==a.crossOrigin?a.crossOrigin:null;this.s=void 0!==a.displayDpi?a.displayDpi:96;this.j=a.params||{};this.T=a.url;this.c=void 0!==a.imageLoadFunction?a.imageLoadFunction:zk;this.ha=void 0!==a.hidpi?a.hidpi:!0;this.na=void 0!==a.metersPerUnit?a.metersPerUnit:1;this.v=void 0!==a.ratio?a.ratio:1;this.za=void 0!==a.useOverlay?a.useOverlay:!1;this.i=null;this.R=0}w(wv,tk);k=wv.prototype;k.Tm=function(){return this.j};
k.Kc=function(a,b,c){b=uk(this,b);c=this.ha?c:1;var d=this.i;if(d&&this.R==this.g&&d.$()==b&&d.f==c&&Nb(d.C(),a))return d;1!=this.v&&(a=a.slice(),lc(a,this.v));var e=[ec(a)/b*c,fc(a)/b*c];if(void 0!==this.T){var d=this.T,f=gc(a),g=this.na,h=ec(a),l=fc(a),m=e[0],n=e[1],p=.0254/this.s,e={OPERATION:this.za?"GETDYNAMICMAPOVERLAYIMAGE":"GETMAPIMAGE",VERSION:"2.0.0",LOCALE:"en",CLIENTAGENT:"ol.source.ImageMapGuide source",CLIP:"1",SETDISPLAYDPI:this.s,SETDISPLAYWIDTH:Math.round(e[0]),SETDISPLAYHEIGHT:Math.round(e[1]),
SETVIEWSCALE:n*h>m*l?h*g/(m*p):l*g/(n*p),SETVIEWCENTERX:f[0],SETVIEWCENTERY:f[1]};za(e,this.j);d=uv(d,e);d=new Ct(a,b,c,this.l,d,this.W,this.c);B(d,"change",this.o,this)}else d=null;this.i=d;this.R=this.g;return d};k.Sm=function(){return this.c};k.Vm=function(a){za(this.j,a);this.u()};k.Um=function(a){this.i=null;this.c=a;this.u()};function xv(a){var b=a.imageExtent,c=void 0!==a.crossOrigin?a.crossOrigin:null,d=void 0!==a.imageLoadFunction?a.imageLoadFunction:zk;tk.call(this,{attributions:a.attributions,logo:a.logo,projection:vc(a.projection)});this.c=new Ct(b,void 0,1,this.l,a.url,c,d);this.i=a.imageSize?a.imageSize:null;B(this.c,"change",this.o,this)}w(xv,tk);xv.prototype.Kc=function(a){return kc(a,this.c.C())?this.c:null};
xv.prototype.o=function(a){if(2==this.c.V()){var b=this.c.C(),c=this.c.a(),d,e;this.i?(d=this.i[0],e=this.i[1]):(d=c.width,e=c.height);b=Math.ceil(ec(b)/(fc(b)/e));if(b!=d){var b=$e(b,e),f=b.canvas;b.drawImage(c,0,0,d,e,0,0,f.width,f.height);this.c.g=f}}tk.prototype.o.call(this,a)};function yv(a){a=a||{};tk.call(this,{attributions:a.attributions,logo:a.logo,projection:a.projection,resolutions:a.resolutions});this.na=void 0!==a.crossOrigin?a.crossOrigin:null;this.j=a.url;this.R=void 0!==a.imageLoadFunction?a.imageLoadFunction:zk;this.i=a.params||{};this.v=!0;zv(this);this.ha=a.serverType;this.za=void 0!==a.hidpi?a.hidpi:!0;this.c=null;this.T=[0,0];this.W=0;this.s=void 0!==a.ratio?a.ratio:1.5}w(yv,tk);var Av=[101,101];k=yv.prototype;
k.an=function(a,b,c,d){if(void 0!==this.j){var e=ic(a,b,0,Av),f={SERVICE:"WMS",VERSION:"1.3.0",REQUEST:"GetFeatureInfo",FORMAT:"image/png",TRANSPARENT:!0,QUERY_LAYERS:this.i.LAYERS};za(f,this.i,d);d=Math.floor((e[3]-a[1])/b);f[this.v?"I":"X"]=Math.floor((a[0]-e[0])/b);f[this.v?"J":"Y"]=d;return Bv(this,e,Av,1,vc(c),f)}};k.cn=function(){return this.i};
k.Kc=function(a,b,c,d){if(void 0===this.j)return null;b=uk(this,b);1==c||this.za&&void 0!==this.ha||(c=1);a=a.slice();var e=(a[0]+a[2])/2,f=(a[1]+a[3])/2,g=b/c,h=ec(a)/g,g=fc(a)/g,l=this.c;if(l&&this.W==this.g&&l.$()==b&&l.f==c&&Nb(l.C(),a))return l;if(1!=this.s){var l=this.s*ec(a)/2,m=this.s*fc(a)/2;a[0]=e-l;a[1]=f-m;a[2]=e+l;a[3]=f+m}e={SERVICE:"WMS",VERSION:"1.3.0",REQUEST:"GetMap",FORMAT:"image/png",TRANSPARENT:!0};za(e,this.i);this.T[0]=Math.ceil(h*this.s);this.T[1]=Math.ceil(g*this.s);d=Bv(this,
a,this.T,c,d,e);this.c=new Ct(a,b,c,this.l,d,this.na,this.R);this.W=this.g;B(this.c,"change",this.o,this);return this.c};k.bn=function(){return this.R};
function Bv(a,b,c,d,e,f){la(void 0!==a.j,9);f[a.v?"CRS":"SRS"]=e.eb;"STYLES"in a.i||(f.STYLES="");if(1!=d)switch(a.ha){case "geoserver":d=90*d+.5|0;f.FORMAT_OPTIONS="FORMAT_OPTIONS"in f?f.FORMAT_OPTIONS+(";dpi:"+d):"dpi:"+d;break;case "mapserver":f.MAP_RESOLUTION=90*d;break;case "carmentaserver":case "qgis":f.DPI=90*d;break;default:la(!1,8)}f.WIDTH=c[0];f.HEIGHT=c[1];c=e.b;var g;a.v&&"ne"==c.substr(0,2)?g=[b[1],b[0],b[3],b[2]]:g=b;f.BBOX=g.join(",");return uv(a.j,f)}k.dn=function(){return this.j};
k.en=function(a){this.c=null;this.R=a;this.u()};k.fn=function(a){a!=this.j&&(this.j=a,this.c=null,this.u())};k.gn=function(a){za(this.i,a);zv(this);this.c=null;this.u()};function zv(a){a.v=0<=tb(a.i.VERSION||"1.3.0")};function Cv(a){a=a||{};var b;void 0!==a.attributions?b=a.attributions:b=[Dv];ov.call(this,{attributions:b,cacheSize:a.cacheSize,crossOrigin:void 0!==a.crossOrigin?a.crossOrigin:"anonymous",opaque:void 0!==a.opaque?a.opaque:!0,maxZoom:void 0!==a.maxZoom?a.maxZoom:19,reprojectionErrorThreshold:a.reprojectionErrorThreshold,tileLoadFunction:a.tileLoadFunction,url:void 0!==a.url?a.url:"https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png",wrapX:a.wrapX})}w(Cv,ov);var Dv=new qe({html:'&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors.'});(function(){var a={},b={ja:a};(function(c){if("object"===typeof a&&"undefined"!==typeof b)b.ja=c();else{var d;"undefined"!==typeof window?d=window:"undefined"!==typeof global?d=global:"undefined"!==typeof self?d=self:d=this;d.Op=c()}})(function(){return function d(a,b,g){function h(m,p){if(!b[m]){if(!a[m]){var q="function"==typeof require&&require;if(!p&&q)return q(m,!0);if(l)return l(m,!0);q=Error("Cannot find module '"+m+"'");throw q.code="MODULE_NOT_FOUND",q;}q=b[m]={ja:{}};a[m][0].call(q.ja,function(b){var d=
a[m][1][b];return h(d?d:b)},q,q.ja,d,a,b,g)}return b[m].ja}for(var l="function"==typeof require&&require,m=0;m<g.length;m++)h(g[m]);return h}({1:[function(a,b,f){a=a("./processor");f.Wi=a},{"./processor":2}],2:[function(a,b){function f(a){var b=!0;try{new ImageData(10,10)}catch(d){b=!1}return function(d){var e=d.buffers,f=d.meta,g=d.width,h=d.height,l=e.length,m=e[0].byteLength;if(d.imageOps){m=Array(l);for(d=0;d<l;++d){var G=m,J=d,X;X=new Uint8ClampedArray(e[d]);var A=g,Ma=h;X=b?new ImageData(X,
A,Ma):{data:X,width:A,height:Ma};G[J]=X}g=a(m,f).data}else{g=new Uint8ClampedArray(m);h=Array(l);G=Array(l);for(d=0;d<l;++d)h[d]=new Uint8ClampedArray(e[d]),G[d]=[0,0,0,0];for(e=0;e<m;e+=4){for(d=0;d<l;++d)J=h[d],G[d][0]=J[e],G[d][1]=J[e+1],G[d][2]=J[e+2],G[d][3]=J[e+3];d=a(G,f);g[e]=d[0];g[e+1]=d[1];g[e+2]=d[2];g[e+3]=d[3]}}return g.buffer}}function g(a,b){var d=Object.keys(a.lib||{}).map(function(b){return"var "+b+" = "+a.lib[b].toString()+";"}).concat(["var __minion__ = ("+f.toString()+")(",a.operation.toString(),
");",'self.addEventListener("message", function(event) {',"  var buffer = __minion__(event.data);","  self.postMessage({buffer: buffer, meta: event.data.meta}, [buffer]);","});"]),d=URL.createObjectURL(new Blob(d,{type:"text/javascript"})),d=new Worker(d);d.addEventListener("message",b);return d}function h(a,b){var d=f(a.operation);return{postMessage:function(a){setTimeout(function(){b({data:{buffer:d(a),meta:a.meta}})},0)}}}function l(a){this.Pe=!!a.gl;var b;0===a.threads?b=0:this.Pe?b=1:b=a.threads||
1;var d=[];if(b)for(var e=0;e<b;++e)d[e]=g(a,this.hg.bind(this,e));else d[0]=h(a,this.hg.bind(this,0));this.Kd=d;this.$c=[];this.kj=a.ro||Infinity;this.Id=0;this.Gc={};this.Qe=null}var m=a("./util").Al;l.prototype.po=function(a,b,d){this.hj({yc:a,Ug:b,pg:d});this.eg()};l.prototype.hj=function(a){for(this.$c.push(a);this.$c.length>this.kj;)this.$c.shift().pg(null,null)};l.prototype.eg=function(){if(0===this.Id&&0<this.$c.length){var a=this.Qe=this.$c.shift(),b=a.yc[0].width,d=a.yc[0].height,e=a.yc.map(function(a){return a.data.buffer}),
f=this.Kd.length;this.Id=f;if(1===f)this.Kd[0].postMessage({buffers:e,meta:a.Ug,imageOps:this.Pe,width:b,height:d},e);else for(var g=4*Math.ceil(a.yc[0].data.length/4/f),h=0;h<f;++h){for(var l=h*g,m=[],G=0,J=e.length;G<J;++G)m.push(e[h].slice(l,l+g));this.Kd[h].postMessage({buffers:m,meta:a.Ug,imageOps:this.Pe,width:b,height:d},m)}}};l.prototype.hg=function(a,b){this.Mp||(this.Gc[a]=b.data,--this.Id,0===this.Id&&this.lj())};l.prototype.lj=function(){var a=this.Qe,b=this.Kd.length,d,e;if(1===b)d=new Uint8ClampedArray(this.Gc[0].buffer),
e=this.Gc[0].meta;else{var f=a.yc[0].data.length;d=new Uint8ClampedArray(f);e=Array(f);for(var f=4*Math.ceil(f/4/b),g=0;g<b;++g){var h=g*f;d.set(new Uint8ClampedArray(this.Gc[g].buffer),h);e[g]=this.Gc[g].meta}}this.Qe=null;this.Gc={};a.pg(null,m(d,a.yc[0].width,a.yc[0].height),e);this.eg()};b.ja=l},{"./util":3}],3:[function(a,b,f){var g=!0;try{new ImageData(10,10)}catch(l){g=!1}var h=document.createElement("canvas").getContext("2d");f.Al=function(a,b,d){if(g)return new ImageData(a,b,d);b=h.createImageData(b,
d);b.data.set(a);return b}},{}]},{},[1])(1)});Qq=b.ja})();function Ev(a){this.R=null;this.za=void 0!==a.operationType?a.operationType:"pixel";this.Ia=void 0!==a.threads?a.threads:1;this.c=Fv(a.sources);for(var b=0,c=this.c.length;b<c;++b)B(this.c[b],"change",this.u,this);this.i=$e();this.ha=new xh(function(){return 1},this.u.bind(this));for(var b=Gv(this.c),c={},d=0,e=b.length;d<e;++d)c[x(b[d].layer)]=b[d];this.j=this.s=null;this.W={animate:!1,attributions:{},coordinateToPixelTransform:ah(),extent:null,focus:null,index:0,layerStates:c,layerStatesArray:b,
logos:{},pixelRatio:1,pixelToCoordinateTransform:ah(),postRenderFunctions:[],size:[0,0],skippedFeatureUids:{},tileQueue:this.ha,time:Date.now(),usedTiles:{},viewState:{rotation:0},viewHints:[],wantedTiles:{}};tk.call(this,{});void 0!==a.operation&&this.v(a.operation,a.lib)}w(Ev,tk);Ev.prototype.v=function(a,b){this.R=new Qq.Wi({operation:a,gl:"image"===this.za,ro:1,lib:b,threads:this.Ia});this.u()};function Hv(a,b,c){var d=a.s;return!d||a.g!==d.Uo||c!==d.resolution||!Ub(b,d.extent)}
Ev.prototype.A=function(a,b,c,d){c=!0;for(var e,f=0,g=this.c.length;f<g;++f)if(e=this.c[f].a.ga(),"ready"!==e.V()){c=!1;break}if(!c)return null;a=a.slice();if(!Hv(this,a,b))return this.j;c=this.i.canvas;e=Math.round(ec(a)/b);f=Math.round(fc(a)/b);if(e!==c.width||f!==c.height)c.width=e,c.height=f;e=za({},this.W);e.viewState=za({},e.viewState);var f=gc(a),g=Math.round(ec(a)/b),h=Math.round(fc(a)/b);e.extent=a;e.focus=gc(a);e.size[0]=g;e.size[1]=h;g=e.viewState;g.center=f;g.projection=d;g.resolution=
b;this.j=d=new hk(a,b,1,this.l,c,this.T.bind(this,e));this.s={extent:a,resolution:b,Uo:this.g};return d};
Ev.prototype.T=function(a,b){for(var c=this.c.length,d=Array(c),e=0;e<c;++e){var f;f=this.c[e];var g=a,h=a.layerStatesArray[e];if(f.l(g,h)){var l=g.size[0],m=g.size[1];if(Iv){var n=Iv.canvas;n.width!==l||n.height!==m?Iv=$e(l,m):Iv.clearRect(0,0,l,m)}else Iv=$e(l,m);f.i(g,h,Iv);f=Iv.getImageData(0,0,l,m)}else f=null;if(f)d[e]=f;else return}c={};this.b(new Jv(Kv,a,c));this.R.po(d,c,this.na.bind(this,a,b));yh(a.tileQueue,16,16)};
Ev.prototype.na=function(a,b,c,d,e){c?b(c):d&&(this.b(new Jv(Lv,a,e)),Hv(this,a.extent,a.viewState.resolution/a.pixelRatio)||this.i.putImageData(d,0,0),b(null))};var Iv=null;function Gv(a){return a.map(function(a){return Rg(a.a)})}function Fv(a){for(var b=a.length,c=Array(b),d=0;d<b;++d){var e=d,f=a[d],g=null;f instanceof ev?(f=new Ki({source:f}),g=new Ek(f)):f instanceof tk&&(f=new Ji({source:f}),g=new Dk(f));c[e]=g}return c}
function Jv(a,b,c){Pa.call(this,a);this.extent=b.extent;this.resolution=b.viewState.resolution/b.pixelRatio;this.data=c}w(Jv,Pa);var Kv="beforeoperations",Lv="afteroperations";var Mv={terrain:{wb:"jpg",opaque:!0},"terrain-background":{wb:"jpg",opaque:!0},"terrain-labels":{wb:"png",opaque:!1},"terrain-lines":{wb:"png",opaque:!1},"toner-background":{wb:"png",opaque:!0},toner:{wb:"png",opaque:!0},"toner-hybrid":{wb:"png",opaque:!1},"toner-labels":{wb:"png",opaque:!1},"toner-lines":{wb:"png",opaque:!1},"toner-lite":{wb:"png",opaque:!0},watercolor:{wb:"jpg",opaque:!0}},Nv={terrain:{minZoom:4,maxZoom:18},toner:{minZoom:0,maxZoom:20},watercolor:{minZoom:1,maxZoom:16}};
function Ov(a){var b=a.layer.indexOf("-"),b=-1==b?a.layer:a.layer.slice(0,b),b=Nv[b],c=Mv[a.layer];ov.call(this,{attributions:Pv,cacheSize:a.cacheSize,crossOrigin:"anonymous",maxZoom:void 0!=a.maxZoom?a.maxZoom:b.maxZoom,minZoom:void 0!=a.minZoom?a.minZoom:b.minZoom,opaque:c.opaque,reprojectionErrorThreshold:a.reprojectionErrorThreshold,tileLoadFunction:a.tileLoadFunction,url:void 0!==a.url?a.url:"https://stamen-tiles-{a-d}.a.ssl.fastly.net/"+a.layer+"/{z}/{x}/{y}."+c.wb})}w(Ov,ov);
var Pv=[new qe({html:'Map tiles by <a href="http://stamen.com/">Stamen Design</a>, under <a href="http://creativecommons.org/licenses/by/3.0/">CC BY 3.0</a>.'}),Dv];function Qv(a){a=a||{};W.call(this,{attributions:a.attributions,cacheSize:a.cacheSize,crossOrigin:a.crossOrigin,logo:a.logo,projection:a.projection,reprojectionErrorThreshold:a.reprojectionErrorThreshold,tileGrid:a.tileGrid,tileLoadFunction:a.tileLoadFunction,url:a.url,urls:a.urls,wrapX:void 0!==a.wrapX?a.wrapX:!0});this.c=a.params||{};this.o=Eb()}w(Qv,W);Qv.prototype.v=function(){return this.c};Qv.prototype.zb=function(a){return a};
Qv.prototype.vc=function(a,b,c){var d=this.tileGrid;d||(d=this.fb(c));if(!(d.b.length<=a[0])){var e=d.Ea(a,this.o),f=ae(d.Qa(a[0]),this.j);1!=b&&(f=$d(f,b,this.j));d={F:"image",FORMAT:"PNG32",TRANSPARENT:!0};za(d,this.c);var g=this.urls;g?(c=c.eb.split(":").pop(),d.SIZE=f[0]+","+f[1],d.BBOX=e.join(","),d.BBOXSR=c,d.IMAGESR=c,d.DPI=Math.round(d.DPI?d.DPI*b:90*b),a=1==g.length?g[0]:g[ta((a[1]<<a[0])+a[2],g.length)],b=a.replace(/MapServer\/?$/,"MapServer/export").replace(/ImageServer\/?$/,"ImageServer/exportImage"),
b==a&&la(!1,50),d=uv(b,d)):d=void 0;return d}};Qv.prototype.A=function(a){za(this.c,a);this.u()};function Rv(a,b,c){qh.call(this,a,2);this.l=b;this.c=c;this.g={}}w(Rv,qh);Rv.prototype.ab=function(a){a=void 0!==a?x(a):-1;if(a in this.g)return this.g[a];var b=this.l,c=$e(b[0],b[1]);c.strokeStyle="black";c.strokeRect(.5,.5,b[0]+.5,b[1]+.5);c.fillStyle="black";c.textAlign="center";c.textBaseline="middle";c.font="24px sans-serif";c.fillText(this.c,b[0]/2,b[1]/2);return this.g[a]=c.canvas};
function Sv(a){ev.call(this,{opaque:!1,projection:a.projection,tileGrid:a.tileGrid,wrapX:void 0!==a.wrapX?a.wrapX:!0})}w(Sv,ev);Sv.prototype.dc=function(a,b,c){var d=this.Ib(a,b,c);if(this.a.b.hasOwnProperty(d))return this.a.get(d);var e=ae(this.tileGrid.Qa(a));a=[a,b,c];b=(b=gv(this,a))?gv(this,b).toString():"";e=new Rv(a,e,b);this.a.set(d,e);return e};function Tv(a){this.c=null;W.call(this,{attributions:a.attributions,cacheSize:a.cacheSize,crossOrigin:a.crossOrigin,projection:vc("EPSG:3857"),reprojectionErrorThreshold:a.reprojectionErrorThreshold,state:"loading",tileLoadFunction:a.tileLoadFunction,wrapX:void 0!==a.wrapX?a.wrapX:!0});if(a.jsonp)Wu(a.url,this.vh.bind(this),this.ke.bind(this));else{var b=new XMLHttpRequest;b.addEventListener("load",this.jn.bind(this));b.addEventListener("error",this.hn.bind(this));b.open("GET",a.url);b.send()}}
w(Tv,W);k=Tv.prototype;k.jn=function(a){a=a.target;if(!a.status||200<=a.status&&300>a.status){var b;try{b=JSON.parse(a.responseText)}catch(c){this.ke();return}this.vh(b)}else this.ke()};k.hn=function(){this.ke()};k.xk=function(){return this.c};
k.vh=function(a){var b=vc("EPSG:4326"),c=this.f,d;if(void 0!==a.bounds){var e=yc(b,c);d=mc(a.bounds,e)}var f=a.minzoom||0,e=a.maxzoom||22;this.tileGrid=c=pe({extent:ne(c),maxZoom:e,minZoom:f});this.tileUrlFunction=Zu(a.tiles,c);if(void 0!==a.attribution&&!this.l){b=void 0!==d?d:b.C();d={};for(var g;f<=e;++f)g=f.toString(),d[g]=[ge(c,b,f)];this.ra([new qe({html:a.attribution,tileRanges:d})])}this.c=a;Vg(this,"ready")};k.ke=function(){Vg(this,"error")};function Uv(a){ev.call(this,{projection:vc("EPSG:3857"),state:"loading"});this.s=void 0!==a.preemptive?a.preemptive:!0;this.o=av;this.i=void 0;this.c=a.jsonp||!1;if(a.url)if(this.c)Wu(a.url,this.Af.bind(this),this.le.bind(this));else{var b=new XMLHttpRequest;b.addEventListener("load",this.nn.bind(this));b.addEventListener("error",this.mn.bind(this));b.open("GET",a.url);b.send()}else a.tileJSON?this.Af(a.tileJSON):la(!1,51)}w(Uv,ev);k=Uv.prototype;
k.nn=function(a){a=a.target;if(!a.status||200<=a.status&&300>a.status){var b;try{b=JSON.parse(a.responseText)}catch(c){this.le();return}this.Af(b)}else this.le()};k.mn=function(){this.le()};k.uk=function(){return this.i};k.Fj=function(a,b,c,d,e){this.tileGrid?(b=this.tileGrid.Yd(a,b),Vv(this.dc(b[0],b[1],b[2],1,this.f),a,c,d,e)):!0===e?Af(function(){c.call(d,null)}):c.call(d,null)};k.le=function(){Vg(this,"error")};
k.Af=function(a){var b=vc("EPSG:4326"),c=this.f,d;if(void 0!==a.bounds){var e=yc(b,c);d=mc(a.bounds,e)}var f=a.minzoom||0,e=a.maxzoom||22;this.tileGrid=c=pe({extent:ne(c),maxZoom:e,minZoom:f});this.i=a.template;var g=a.grids;if(g){this.o=Zu(g,c);if(void 0!==a.attribution){b=void 0!==d?d:b.C();for(d={};f<=e;++f)g=f.toString(),d[g]=[ge(c,b,f)];this.ra([new qe({html:a.attribution,tileRanges:d})])}Vg(this,"ready")}else Vg(this,"error")};
k.dc=function(a,b,c,d,e){var f=this.Ib(a,b,c);if(this.a.b.hasOwnProperty(f))return this.a.get(f);a=[a,b,c];b=gv(this,a,e);d=this.o(b,d,e);d=new Wv(a,void 0!==d?0:4,void 0!==d?d:"",this.tileGrid.Ea(a),this.s,this.c);this.a.set(f,d);return d};k.Xf=function(a,b,c){a=this.Ib(a,b,c);this.a.b.hasOwnProperty(a)&&this.a.get(a)};function Wv(a,b,c,d,e,f){qh.call(this,a,b);this.s=c;this.g=d;this.U=e;this.c=this.o=this.l=null;this.v=f}w(Wv,qh);k=Wv.prototype;k.ab=function(){return null};
k.getData=function(a){if(!this.l||!this.o)return null;var b=this.l[Math.floor((1-(a[1]-this.g[1])/(this.g[3]-this.g[1]))*this.l.length)];if("string"!==typeof b)return null;b=b.charCodeAt(Math.floor((a[0]-this.g[0])/(this.g[2]-this.g[0])*b.length));93<=b&&b--;35<=b&&b--;b-=32;a=null;b in this.o&&(b=this.o[b],this.c&&b in this.c?a=this.c[b]:a=b);return a};
function Vv(a,b,c,d,e){0==a.state&&!0===e?(Ja(a,"change",function(){c.call(d,this.getData(b))},a),Xv(a)):!0===e?Af(function(){c.call(d,this.getData(b))},a):c.call(d,a.getData(b))}k.Ya=function(){return this.s};k.Zd=function(){this.state=3;rh(this)};k.wh=function(a){this.l=a.grid;this.o=a.keys;this.c=a.data;this.state=4;rh(this)};
function Xv(a){if(0==a.state)if(a.state=1,a.v)Wu(a.s,a.wh.bind(a),a.Zd.bind(a));else{var b=new XMLHttpRequest;b.addEventListener("load",a.ln.bind(a));b.addEventListener("error",a.kn.bind(a));b.open("GET",a.s);b.send()}}k.ln=function(a){a=a.target;if(!a.status||200<=a.status&&300>a.status){var b;try{b=JSON.parse(a.responseText)}catch(c){this.Zd();return}this.wh(b)}else this.Zd()};k.kn=function(){this.Zd()};k.load=function(){this.U&&Xv(this)};function Yv(a){a=a||{};var b=a.params||{};W.call(this,{attributions:a.attributions,cacheSize:a.cacheSize,crossOrigin:a.crossOrigin,logo:a.logo,opaque:!("TRANSPARENT"in b?b.TRANSPARENT:1),projection:a.projection,reprojectionErrorThreshold:a.reprojectionErrorThreshold,tileGrid:a.tileGrid,tileLoadFunction:a.tileLoadFunction,url:a.url,urls:a.urls,wrapX:void 0!==a.wrapX?a.wrapX:!0});this.v=void 0!==a.gutter?a.gutter:0;this.c=b;this.o=!0;this.A=a.serverType;this.T=void 0!==a.hidpi?a.hidpi:!0;this.R="";
Zv(this);this.W=Eb();$v(this);fv(this,aw(this))}w(Yv,W);k=Yv.prototype;
k.pn=function(a,b,c,d){c=vc(c);var e=this.tileGrid;e||(e=this.fb(c));b=e.Yd(a,b);if(!(e.b.length<=b[0])){var f=e.$(b[0]),g=e.Ea(b,this.W),e=ae(e.Qa(b[0]),this.j),h=this.v;0!==h&&(e=Zd(e,h,this.j),g=Gb(g,f*h,g));h={SERVICE:"WMS",VERSION:"1.3.0",REQUEST:"GetFeatureInfo",FORMAT:"image/png",TRANSPARENT:!0,QUERY_LAYERS:this.c.LAYERS};za(h,this.c,d);d=Math.floor((g[3]-a[1])/f);h[this.o?"I":"X"]=Math.floor((a[0]-g[0])/f);h[this.o?"J":"Y"]=d;return bw(this,b,e,g,1,c,h)}};k.ef=function(){return this.v};
k.Ib=function(a,b,c){return this.R+W.prototype.Ib.call(this,a,b,c)};k.qn=function(){return this.c};
function bw(a,b,c,d,e,f,g){var h=a.urls;if(h){g.WIDTH=c[0];g.HEIGHT=c[1];g[a.o?"CRS":"SRS"]=f.eb;"STYLES"in a.c||(g.STYLES="");if(1!=e)switch(a.A){case "geoserver":c=90*e+.5|0;g.FORMAT_OPTIONS="FORMAT_OPTIONS"in g?g.FORMAT_OPTIONS+(";dpi:"+c):"dpi:"+c;break;case "mapserver":g.MAP_RESOLUTION=90*e;break;case "carmentaserver":case "qgis":g.DPI=90*e;break;default:la(!1,52)}f=f.b;a.o&&"ne"==f.substr(0,2)&&(a=d[0],d[0]=d[1],d[1]=a,a=d[2],d[2]=d[3],d[3]=a);g.BBOX=d.join(",");return uv(1==h.length?h[0]:h[ta((b[1]<<
b[0])+b[2],h.length)],g)}}k.zb=function(a){return this.T&&void 0!==this.A?a:1};function Zv(a){var b=0,c=[];if(a.urls){var d,e;d=0;for(e=a.urls.length;d<e;++d)c[b++]=a.urls[d]}a.R=c.join("#")}function aw(a){var b=0,c=[],d;for(d in a.c)c[b++]=d+"-"+a.c[d];return c.join("/")}
k.vc=function(a,b,c){var d=this.tileGrid;d||(d=this.fb(c));if(!(d.b.length<=a[0])){1==b||this.T&&void 0!==this.A||(b=1);var e=d.$(a[0]),f=d.Ea(a,this.W),d=ae(d.Qa(a[0]),this.j),g=this.v;0!==g&&(d=Zd(d,g,this.j),f=Gb(f,e*g,f));1!=b&&(d=$d(d,b,this.j));e={SERVICE:"WMS",VERSION:"1.3.0",REQUEST:"GetMap",FORMAT:"image/png",TRANSPARENT:!0};za(e,this.c);return bw(this,a,d,f,b,c,e)}};k.Ua=function(a){W.prototype.Ua.call(this,a);Zv(this)};k.rn=function(a){za(this.c,a);Zv(this);$v(this);fv(this,aw(this))};
function $v(a){a.o=0<=tb(a.c.VERSION||"1.3.0")};function cw(a,b,c,d,e){qh.call(this,a,b);this.g=$e();this.l=d;this.c=null;this.f={gd:!1,Sf:null,Zh:-1,Tf:-1,xd:null,ri:[]};this.v=e;this.o=c}w(cw,qh);k=cw.prototype;k.ab=function(){return-1==this.f.Tf?null:this.g.canvas};k.Pl=function(){return this.l};k.Ya=function(){return this.o};k.load=function(){0==this.state&&(this.state=1,rh(this),this.v(this,this.o),this.s(null,NaN,null))};k.di=function(a){this.c=a;this.state=2;rh(this)};k.uf=function(a){this.j=a};k.hi=function(a){this.s=a};function dw(a){iv.call(this,{attributions:a.attributions,cacheSize:void 0!==a.cacheSize?a.cacheSize:128,extent:a.extent,logo:a.logo,opaque:!1,projection:a.projection,state:a.state,tileGrid:a.tileGrid,tileLoadFunction:a.tileLoadFunction?a.tileLoadFunction:ew,tileUrlFunction:a.tileUrlFunction,tilePixelRatio:a.tilePixelRatio,url:a.url,urls:a.urls,wrapX:void 0===a.wrapX?!0:a.wrapX});this.c=a.format?a.format:null;this.i=void 0==a.overlaps?!0:a.overlaps;this.tileClass=a.tileClass?a.tileClass:cw}w(dw,iv);
dw.prototype.dc=function(a,b,c,d,e){var f=this.Ib(a,b,c);if(this.a.b.hasOwnProperty(f))return this.a.get(f);a=[a,b,c];d=(b=gv(this,a,e))?this.tileUrlFunction(b,d,e):void 0;d=new this.tileClass(a,void 0!==d?0:4,void 0!==d?d:"",this.c,this.tileLoadFunction);B(d,"change",this.yh,this);this.a.set(f,d);return d};dw.prototype.zb=function(a){return void 0==a?iv.prototype.zb.call(this,a):a};dw.prototype.jf=function(a,b){var c=ae(this.tileGrid.Qa(a));return[Math.round(c[0]*b),Math.round(c[1]*b)]};
function ew(a,b){a.hi(Vm(b,a.l))};function fw(a){this.j=a.matrixIds;de.call(this,{extent:a.extent,origin:a.origin,origins:a.origins,resolutions:a.resolutions,tileSize:a.tileSize,tileSizes:a.tileSizes,sizes:a.sizes})}w(fw,de);fw.prototype.o=function(){return this.j};
function gw(a,b){var c=[],d=[],e=[],f=[],g=[],h;h=vc(a.SupportedCRS.replace(/urn:ogc:def:crs:(\w+):(.*:)?(\w+)$/,"$1:$3"));var l=h.cc(),m="ne"==h.b.substr(0,2);a.TileMatrix.sort(function(a,b){return b.ScaleDenominator-a.ScaleDenominator});a.TileMatrix.forEach(function(a){d.push(a.Identifier);var b=2.8E-4*a.ScaleDenominator/l,h=a.TileWidth,t=a.TileHeight;m?e.push([a.TopLeftCorner[1],a.TopLeftCorner[0]]):e.push(a.TopLeftCorner);c.push(b);f.push(h==t?h:[h,t]);g.push([a.MatrixWidth,-a.MatrixHeight])});
return new fw({extent:b,origins:e,resolutions:c,matrixIds:d,tileSizes:f,sizes:g})};function Z(a){function b(a){a="KVP"==d?uv(a,f):a.replace(/\{(\w+?)\}/g,function(a,b){return b.toLowerCase()in f?f[b.toLowerCase()]:a});return function(b){if(b){var c={TileMatrix:e.j[b[0]],TileCol:b[1],TileRow:-b[2]-1};za(c,g);b=a;return b="KVP"==d?uv(b,c):b.replace(/\{(\w+?)\}/g,function(a,b){return c[b]})}}}this.W=void 0!==a.version?a.version:"1.0.0";this.v=void 0!==a.format?a.format:"image/jpeg";this.c=void 0!==a.dimensions?a.dimensions:{};this.A=a.layer;this.o=a.matrixSet;this.R=a.style;var c=
a.urls;void 0===c&&void 0!==a.url&&(c=bv(a.url));var d=this.T=void 0!==a.requestEncoding?a.requestEncoding:"KVP",e=a.tileGrid,f={layer:this.A,style:this.R,tilematrixset:this.o};"KVP"==d&&za(f,{Service:"WMTS",Request:"GetTile",Version:this.W,Format:this.v});var g=this.c,h=c&&0<c.length?$u(c.map(b)):av;W.call(this,{attributions:a.attributions,cacheSize:a.cacheSize,crossOrigin:a.crossOrigin,logo:a.logo,projection:a.projection,reprojectionErrorThreshold:a.reprojectionErrorThreshold,tileClass:a.tileClass,
tileGrid:e,tileLoadFunction:a.tileLoadFunction,tilePixelRatio:a.tilePixelRatio,tileUrlFunction:h,urls:c,wrapX:void 0!==a.wrapX?a.wrapX:!1});fv(this,hw(this))}w(Z,W);k=Z.prototype;k.Sj=function(){return this.c};k.sn=function(){return this.v};k.tn=function(){return this.A};k.ek=function(){return this.o};k.sk=function(){return this.T};k.vn=function(){return this.R};k.zk=function(){return this.W};function hw(a){var b=0,c=[],d;for(d in a.c)c[b++]=d+"-"+a.c[d];return c.join("/")}
k.sp=function(a){za(this.c,a);fv(this,hw(this))};function iw(a){a=a||{};var b=a.size,c=b[0],d=b[1],e=[],f=256;switch(void 0!==a.tierSizeCalculation?a.tierSizeCalculation:"default"){case "default":for(;c>f||d>f;)e.push([Math.ceil(c/f),Math.ceil(d/f)]),f+=f;break;case "truncated":for(;c>f||d>f;)e.push([Math.ceil(c/f),Math.ceil(d/f)]),c>>=1,d>>=1;break;default:la(!1,53)}e.push([1,1]);e.reverse();for(var f=[1],g=[0],d=1,c=e.length;d<c;d++)f.push(1<<d),g.push(e[d-1][0]*e[d-1][1]+g[d-1]);f.reverse();var b=[0,-b[1],b[0],0],b=new de({extent:b,origin:bc(b),
resolutions:f}),h=a.url;W.call(this,{attributions:a.attributions,cacheSize:a.cacheSize,crossOrigin:a.crossOrigin,logo:a.logo,reprojectionErrorThreshold:a.reprojectionErrorThreshold,tileClass:jw,tileGrid:b,tileUrlFunction:function(a){if(a){var b=a[0],c=a[1];a=-a[2]-1;return h+"TileGroup"+((c+a*e[b][0]+g[b])/256|0)+"/"+b+"-"+c+"-"+a+".jpg"}}})}w(iw,W);function jw(a,b,c,d,e){Dt.call(this,a,b,c,d,e);this.l={}}w(jw,Dt);
jw.prototype.ab=function(a){var b=void 0!==a?x(a).toString():"";if(b in this.l)return this.l[b];a=Dt.prototype.ab.call(this,a);if(2==this.state){if(256==a.width&&256==a.height)return this.l[b]=a;var c=$e(256,256);c.drawImage(a,0,0);return this.l[b]=c.canvas}return a};function kw(a){a=a||{};this.a=void 0!==a.initialSize?a.initialSize:256;this.g=void 0!==a.maxSize?a.maxSize:void 0!==fa?fa:2048;this.b=void 0!==a.space?a.space:1;this.c=[new lw(this.a,this.b)];this.f=this.a;this.i=[new lw(this.f,this.b)]}kw.prototype.add=function(a,b,c,d,e,f){if(b+this.b>this.g||c+this.b>this.g)return null;d=mw(this,!1,a,b,c,d,f);if(!d)return null;a=mw(this,!0,a,b,c,void 0!==e?e:ha,f);return{offsetX:d.offsetX,offsetY:d.offsetY,image:d.image,Qg:a.image}};
function mw(a,b,c,d,e,f,g){var h=b?a.i:a.c,l,m,n;m=0;for(n=h.length;m<n;++m){l=h[m];if(l=l.add(c,d,e,f,g))return l;l||m!==n-1||(b?(l=Math.min(2*a.f,a.g),a.f=l):(l=Math.min(2*a.a,a.g),a.a=l),l=new lw(l,a.b),h.push(l),++n)}return null}function lw(a,b){this.b=b;this.a=[{x:0,y:0,width:a,height:a}];this.f={};this.g=$e(a,a);this.c=this.g.canvas}lw.prototype.get=function(a){return this.f[a]||null};
lw.prototype.add=function(a,b,c,d,e){var f,g,h;g=0;for(h=this.a.length;g<h;++g)if(f=this.a[g],f.width>=b+this.b&&f.height>=c+this.b)return h={offsetX:f.x+this.b,offsetY:f.y+this.b,image:this.c},this.f[a]=h,d.call(e,this.g,f.x+this.b,f.y+this.b),a=g,b+=this.b,d=c+this.b,f.width-b>f.height-d?(c={x:f.x+b,y:f.y,width:f.width-b,height:f.height},b={x:f.x,y:f.y+d,width:b,height:f.height-d},nw(this,a,c,b)):(c={x:f.x+b,y:f.y,width:f.width-b,height:d},b={x:f.x,y:f.y+d,width:f.width,height:f.height-d},nw(this,
a,c,b)),h;return null};function nw(a,b,c,d){b=[b,1];0<c.width&&0<c.height&&b.push(c);0<d.width&&0<d.height&&b.push(d);a.a.splice.apply(a.a,b)};function ow(a){this.A=this.s=this.f=null;this.j=void 0!==a.fill?a.fill:null;this.Aa=[0,0];this.b=a.points;this.g=void 0!==a.radius?a.radius:a.radius1;this.c=void 0!==a.radius2?a.radius2:this.g;this.l=void 0!==a.angle?a.angle:0;this.a=void 0!==a.stroke?a.stroke:null;this.O=this.Ba=this.D=null;var b=a.atlasManager,c="",d="",e=0,f=null,g,h=0;this.a&&(g=Ce(this.a.b),h=this.a.a,void 0===h&&(h=1),f=this.a.g,Lf||(f=null),d=this.a.c,void 0===d&&(d="round"),c=this.a.f,void 0===c&&(c="round"),e=this.a.i,void 0===
e&&(e=10));var l=2*(this.g+h)+1,c={strokeStyle:g,Ad:h,size:l,lineCap:c,lineDash:f,lineJoin:d,miterLimit:e};if(void 0===b){var m=$e(l,l);this.s=m.canvas;b=l=this.s.width;this.Fh(c,m,0,0);this.j?this.A=this.s:(m=$e(c.size,c.size),this.A=m.canvas,this.Eh(c,m,0,0))}else l=Math.round(l),(d=!this.j)&&(m=this.Eh.bind(this,c)),e=this.a?Ri(this.a):"-",f=this.j?Si(this.j):"-",this.f&&e==this.f[1]&&f==this.f[2]&&this.g==this.f[3]&&this.c==this.f[4]&&this.l==this.f[5]&&this.b==this.f[6]||(this.f=["r"+e+f+(void 0!==
this.g?this.g.toString():"-")+(void 0!==this.c?this.c.toString():"-")+(void 0!==this.l?this.l.toString():"-")+(void 0!==this.b?this.b.toString():"-"),e,f,this.g,this.c,this.l,this.b]),m=b.add(this.f[0],l,l,this.Fh.bind(this,c),m),this.s=m.image,this.Aa=[m.offsetX,m.offsetY],b=m.image.width,this.A=d?m.Qg:this.s;this.D=[l/2,l/2];this.Ba=[l,l];this.O=[b,b];Pi.call(this,{opacity:1,rotateWithView:void 0!==a.rotateWithView?a.rotateWithView:!1,rotation:void 0!==a.rotation?a.rotation:0,scale:1,snapToPixel:void 0!==
a.snapToPixel?a.snapToPixel:!0})}w(ow,Pi);k=ow.prototype;k.bc=function(){return this.D};k.An=function(){return this.l};k.Bn=function(){return this.j};k.ne=function(){return this.A};k.mc=function(){return this.s};k.kd=function(){return this.O};k.sd=function(){return 2};k.Ka=function(){return this.Aa};k.Cn=function(){return this.b};k.Dn=function(){return this.g};k.rk=function(){return this.c};k.Jb=function(){return this.Ba};k.En=function(){return this.a};k.nf=ha;k.load=ha;k.Wf=ha;
k.Fh=function(a,b,c,d){var e;b.setTransform(1,0,0,1,0,0);b.translate(c,d);b.beginPath();this.c!==this.g&&(this.b*=2);for(c=0;c<=this.b;c++)d=2*c*Math.PI/this.b-Math.PI/2+this.l,e=0===c%2?this.g:this.c,b.lineTo(a.size/2+e*Math.cos(d),a.size/2+e*Math.sin(d));this.j&&(b.fillStyle=Ee(this.j.b),b.fill());this.a&&(b.strokeStyle=a.strokeStyle,b.lineWidth=a.Ad,a.lineDash&&b.setLineDash(a.lineDash),b.lineCap=a.lineCap,b.lineJoin=a.lineJoin,b.miterLimit=a.miterLimit,b.stroke());b.closePath()};
k.Eh=function(a,b,c,d){b.setTransform(1,0,0,1,0,0);b.translate(c,d);b.beginPath();this.c!==this.g&&(this.b*=2);var e;for(c=0;c<=this.b;c++)e=2*c*Math.PI/this.b-Math.PI/2+this.l,d=0===c%2?this.g:this.c,b.lineTo(a.size/2+d*Math.cos(e),a.size/2+d*Math.sin(e));b.fillStyle=Li;b.fill();this.a&&(b.strokeStyle=a.strokeStyle,b.lineWidth=a.Ad,a.lineDash&&b.setLineDash(a.lineDash),b.stroke());b.closePath()};r("ol.animation.bounce",function(a){var b=a.resolution,c=a.start?a.start:Date.now(),d=void 0!==a.duration?a.duration:1E3,e=a.easing?a.easing:Rd;return function(a,g){if(g.time<c)return g.animate=!0,g.viewHints[0]+=1,!0;if(g.time<c+d){var h=e((g.time-c)/d),l=b-g.viewState.resolution;g.animate=!0;g.viewState.resolution+=h*l;g.viewHints[0]+=1;return!0}return!1}});r("ol.animation.pan",Sd);r("ol.animation.rotate",Td);r("ol.animation.zoom",Ud);ka.prototype.code=ka.prototype.code;r("ol.Attribution",qe);
qe.prototype.getHTML=qe.prototype.g;r("ol.Collection",re);re.prototype.clear=re.prototype.clear;re.prototype.extend=re.prototype.pf;re.prototype.forEach=re.prototype.forEach;re.prototype.getArray=re.prototype.Bl;re.prototype.item=re.prototype.item;re.prototype.getLength=re.prototype.gc;re.prototype.insertAt=re.prototype.ce;re.prototype.pop=re.prototype.pop;re.prototype.push=re.prototype.push;re.prototype.remove=re.prototype.remove;re.prototype.removeAt=re.prototype.Qf;re.prototype.setAt=re.prototype.Wo;
te.prototype.element=te.prototype.element;r("ol.color.asArray",Ae);r("ol.color.asString",Ce);r("ol.colorlike.asColorLike",Ee);r("ol.coordinate.add",ub);r("ol.coordinate.createStringXY",function(a){return function(b){return Cb(b,a)}});r("ol.coordinate.format",xb);r("ol.coordinate.rotate",zb);r("ol.coordinate.toStringHDMS",function(a,b){return a?wb(a[1],"NS",b)+" "+wb(a[0],"EW",b):""});r("ol.coordinate.toStringXY",Cb);r("ol.DeviceOrientation",zm);zm.prototype.getAlpha=zm.prototype.Lj;
zm.prototype.getBeta=zm.prototype.Oj;zm.prototype.getGamma=zm.prototype.Vj;zm.prototype.getHeading=zm.prototype.Cl;zm.prototype.getTracking=zm.prototype.Xg;zm.prototype.setTracking=zm.prototype.qf;r("ol.easing.easeIn",Ld);r("ol.easing.easeOut",Md);r("ol.easing.inAndOut",Nd);r("ol.easing.linear",Qd);r("ol.easing.upAndDown",Rd);r("ol.extent.boundingExtent",Db);r("ol.extent.buffer",Gb);r("ol.extent.containsCoordinate",Lb);r("ol.extent.containsExtent",Nb);r("ol.extent.containsXY",Mb);
r("ol.extent.createEmpty",Eb);r("ol.extent.equals",Ub);r("ol.extent.extend",Vb);r("ol.extent.getBottomLeft",Zb);r("ol.extent.getBottomRight",$b);r("ol.extent.getCenter",gc);r("ol.extent.getHeight",fc);r("ol.extent.getIntersection",jc);r("ol.extent.getSize",function(a){return[a[2]-a[0],a[3]-a[1]]});r("ol.extent.getTopLeft",bc);r("ol.extent.getTopRight",ac);r("ol.extent.getWidth",ec);r("ol.extent.intersects",kc);r("ol.extent.isEmpty",dc);r("ol.extent.applyTransform",mc);r("ol.Feature",Am);
Am.prototype.clone=Am.prototype.clone;Am.prototype.getGeometry=Am.prototype.Y;Am.prototype.getId=Am.prototype.Oa;Am.prototype.getGeometryName=Am.prototype.Xj;Am.prototype.getStyle=Am.prototype.El;Am.prototype.getStyleFunction=Am.prototype.hc;Am.prototype.setGeometry=Am.prototype.Xa;Am.prototype.setStyle=Am.prototype.rf;Am.prototype.setId=Am.prototype.Xb;Am.prototype.setGeometryName=Am.prototype.Cc;r("ol.featureloader.tile",Vm);r("ol.featureloader.xhr",Wm);r("ol.Geolocation",rt);
rt.prototype.getAccuracy=rt.prototype.Jj;rt.prototype.getAccuracyGeometry=rt.prototype.Kj;rt.prototype.getAltitude=rt.prototype.Mj;rt.prototype.getAltitudeAccuracy=rt.prototype.Nj;rt.prototype.getHeading=rt.prototype.Gl;rt.prototype.getPosition=rt.prototype.Hl;rt.prototype.getProjection=rt.prototype.Yg;rt.prototype.getSpeed=rt.prototype.tk;rt.prototype.getTracking=rt.prototype.Zg;rt.prototype.getTrackingOptions=rt.prototype.Kg;rt.prototype.setProjection=rt.prototype.$g;rt.prototype.setTracking=rt.prototype.ee;
rt.prototype.setTrackingOptions=rt.prototype.oi;r("ol.Graticule",xt);xt.prototype.getMap=xt.prototype.Kl;xt.prototype.getMeridians=xt.prototype.fk;xt.prototype.getParallels=xt.prototype.mk;xt.prototype.setMap=xt.prototype.setMap;r("ol.has.DEVICE_PIXEL_RATIO",Kf);r("ol.has.CANVAS",Mf);r("ol.has.DEVICE_ORIENTATION",Nf);r("ol.has.GEOLOCATION",Of);r("ol.has.TOUCH",Pf);r("ol.has.WEBGL",Ff);Ct.prototype.getImage=Ct.prototype.a;Ct.prototype.load=Ct.prototype.load;Dt.prototype.getImage=Dt.prototype.ab;
Dt.prototype.load=Dt.prototype.load;r("ol.inherits",w);r("ol.Kinetic",zh);r("ol.loadingstrategy.all",Ot);r("ol.loadingstrategy.bbox",function(a){return[a]});r("ol.loadingstrategy.tile",function(a){return function(b,c){var d=a.ec(c),e=ge(a,b,d),f=[],d=[d,0,0];for(d[1]=e.ca;d[1]<=e.ea;++d[1])for(d[2]=e.fa;d[2]<=e.ia;++d[2])f.push(a.Ea(d));return f}});r("ol.Map",I);I.prototype.addControl=I.prototype.qj;I.prototype.addInteraction=I.prototype.rj;I.prototype.addLayer=I.prototype.jg;
I.prototype.addOverlay=I.prototype.kg;I.prototype.beforeRender=I.prototype.$a;I.prototype.forEachFeatureAtPixel=I.prototype.Rd;I.prototype.forEachLayerAtPixel=I.prototype.Ol;I.prototype.hasFeatureAtPixel=I.prototype.fl;I.prototype.getEventCoordinate=I.prototype.Tj;I.prototype.getEventPixel=I.prototype.Td;I.prototype.getTarget=I.prototype.sf;I.prototype.getTargetElement=I.prototype.xc;I.prototype.getCoordinateFromPixel=I.prototype.Na;I.prototype.getControls=I.prototype.Rj;I.prototype.getOverlays=I.prototype.kk;
I.prototype.getOverlayById=I.prototype.jk;I.prototype.getInteractions=I.prototype.Yj;I.prototype.getLayerGroup=I.prototype.wc;I.prototype.getLayers=I.prototype.ah;I.prototype.getPixelFromCoordinate=I.prototype.Ga;I.prototype.getSize=I.prototype.kb;I.prototype.getView=I.prototype.aa;I.prototype.getViewport=I.prototype.Ak;I.prototype.renderSync=I.prototype.So;I.prototype.render=I.prototype.render;I.prototype.removeControl=I.prototype.Lo;I.prototype.removeInteraction=I.prototype.Mo;
I.prototype.removeLayer=I.prototype.Oo;I.prototype.removeOverlay=I.prototype.Po;I.prototype.setLayerGroup=I.prototype.gi;I.prototype.setSize=I.prototype.Vf;I.prototype.setTarget=I.prototype.bh;I.prototype.setView=I.prototype.hp;I.prototype.updateSize=I.prototype.Wc;Cg.prototype.originalEvent=Cg.prototype.originalEvent;Cg.prototype.pixel=Cg.prototype.pixel;Cg.prototype.coordinate=Cg.prototype.coordinate;Cg.prototype.dragging=Cg.prototype.dragging;hf.prototype.map=hf.prototype.map;
hf.prototype.frameState=hf.prototype.frameState;Wa.prototype.key=Wa.prototype.key;Wa.prototype.oldValue=Wa.prototype.oldValue;r("ol.Object",Xa);Xa.prototype.get=Xa.prototype.get;Xa.prototype.getKeys=Xa.prototype.P;Xa.prototype.getProperties=Xa.prototype.N;Xa.prototype.set=Xa.prototype.set;Xa.prototype.setProperties=Xa.prototype.G;Xa.prototype.unset=Xa.prototype.S;r("ol.Observable",Ua);r("ol.Observable.unByKey",Va);Ua.prototype.changed=Ua.prototype.u;Ua.prototype.dispatchEvent=Ua.prototype.b;
Ua.prototype.getRevision=Ua.prototype.K;Ua.prototype.on=Ua.prototype.I;Ua.prototype.once=Ua.prototype.L;Ua.prototype.un=Ua.prototype.J;Ua.prototype.unByKey=Ua.prototype.M;r("ol.Overlay",am);am.prototype.getElement=am.prototype.Sd;am.prototype.getId=am.prototype.Oa;am.prototype.getMap=am.prototype.fe;am.prototype.getOffset=am.prototype.Ig;am.prototype.getPosition=am.prototype.dh;am.prototype.getPositioning=am.prototype.Jg;am.prototype.setElement=am.prototype.ci;am.prototype.setMap=am.prototype.setMap;
am.prototype.setOffset=am.prototype.ii;am.prototype.setPosition=am.prototype.tf;am.prototype.setPositioning=am.prototype.li;r("ol.render.toContext",function(a,b){var c=a.canvas,d=b?b:{},e=d.pixelRatio||Kf;if(d=d.size)c.width=d[0]*e,c.height=d[1]*e,c.style.width=d[0]+"px",c.style.height=d[1]+"px";c=[0,0,c.width,c.height];d=hh(ah(),e,e);return new gj(a,e,c,d,0)});r("ol.size.toSize",ae);qh.prototype.getTileCoord=qh.prototype.i;qh.prototype.load=qh.prototype.load;cw.prototype.getFormat=cw.prototype.Pl;
cw.prototype.setFeatures=cw.prototype.di;cw.prototype.setProjection=cw.prototype.uf;cw.prototype.setLoader=cw.prototype.hi;r("ol.View",Bd);Bd.prototype.constrainCenter=Bd.prototype.Od;Bd.prototype.constrainResolution=Bd.prototype.constrainResolution;Bd.prototype.constrainRotation=Bd.prototype.constrainRotation;Bd.prototype.getCenter=Bd.prototype.bb;Bd.prototype.calculateExtent=Bd.prototype.Ic;Bd.prototype.getMaxResolution=Bd.prototype.Ql;Bd.prototype.getMinResolution=Bd.prototype.Rl;
Bd.prototype.getProjection=Bd.prototype.Sl;Bd.prototype.getResolution=Bd.prototype.$;Bd.prototype.getResolutions=Bd.prototype.Tl;Bd.prototype.getRotation=Bd.prototype.Ma;Bd.prototype.getZoom=Bd.prototype.Ck;Bd.prototype.fit=Bd.prototype.af;Bd.prototype.centerOn=Bd.prototype.Aj;Bd.prototype.rotate=Bd.prototype.rotate;Bd.prototype.setCenter=Bd.prototype.ob;Bd.prototype.setResolution=Bd.prototype.Zb;Bd.prototype.setRotation=Bd.prototype.ge;Bd.prototype.setZoom=Bd.prototype.kp;
r("ol.xml.getAllTextContent",Em);r("ol.xml.parse",Im);el.prototype.getGL=el.prototype.Xn;el.prototype.useProgram=el.prototype.ve;r("ol.tilegrid.createXYZ",pe);r("ol.tilegrid.TileGrid",de);de.prototype.forEachTileCoord=de.prototype.wg;de.prototype.getMaxZoom=de.prototype.Gg;de.prototype.getMinZoom=de.prototype.Hg;de.prototype.getOrigin=de.prototype.Ka;de.prototype.getResolution=de.prototype.$;de.prototype.getResolutions=de.prototype.Hh;de.prototype.getTileCoordExtent=de.prototype.Ea;
de.prototype.getTileCoordForCoordAndResolution=de.prototype.Yd;de.prototype.getTileCoordForCoordAndZ=de.prototype.pd;de.prototype.getTileSize=de.prototype.Qa;de.prototype.getZForResolution=de.prototype.ec;r("ol.tilegrid.WMTS",fw);fw.prototype.getMatrixIds=fw.prototype.o;r("ol.tilegrid.WMTS.createFromCapabilitiesMatrixSet",gw);r("ol.style.AtlasManager",kw);r("ol.style.Circle",Qi);Qi.prototype.getFill=Qi.prototype.wn;Qi.prototype.getImage=Qi.prototype.mc;Qi.prototype.getRadius=Qi.prototype.xn;
Qi.prototype.getStroke=Qi.prototype.yn;r("ol.style.Fill",Ti);Ti.prototype.getColor=Ti.prototype.g;Ti.prototype.setColor=Ti.prototype.f;r("ol.style.Icon",Qo);Qo.prototype.getAnchor=Qo.prototype.bc;Qo.prototype.getImage=Qo.prototype.mc;Qo.prototype.getOrigin=Qo.prototype.Ka;Qo.prototype.getSrc=Qo.prototype.zn;Qo.prototype.getSize=Qo.prototype.Jb;Qo.prototype.load=Qo.prototype.load;r("ol.style.Image",Pi);Pi.prototype.getOpacity=Pi.prototype.oe;Pi.prototype.getRotateWithView=Pi.prototype.pe;
Pi.prototype.getRotation=Pi.prototype.qe;Pi.prototype.getScale=Pi.prototype.re;Pi.prototype.getSnapToPixel=Pi.prototype.Xd;Pi.prototype.setOpacity=Pi.prototype.se;Pi.prototype.setRotation=Pi.prototype.te;Pi.prototype.setScale=Pi.prototype.ue;r("ol.style.RegularShape",ow);ow.prototype.getAnchor=ow.prototype.bc;ow.prototype.getAngle=ow.prototype.An;ow.prototype.getFill=ow.prototype.Bn;ow.prototype.getImage=ow.prototype.mc;ow.prototype.getOrigin=ow.prototype.Ka;ow.prototype.getPoints=ow.prototype.Cn;
ow.prototype.getRadius=ow.prototype.Dn;ow.prototype.getRadius2=ow.prototype.rk;ow.prototype.getSize=ow.prototype.Jb;ow.prototype.getStroke=ow.prototype.En;r("ol.style.Stroke",Yi);Yi.prototype.getColor=Yi.prototype.Fn;Yi.prototype.getLineCap=Yi.prototype.ak;Yi.prototype.getLineDash=Yi.prototype.Gn;Yi.prototype.getLineJoin=Yi.prototype.bk;Yi.prototype.getMiterLimit=Yi.prototype.gk;Yi.prototype.getWidth=Yi.prototype.Hn;Yi.prototype.setColor=Yi.prototype.In;Yi.prototype.setLineCap=Yi.prototype.bp;
Yi.prototype.setLineDash=Yi.prototype.Jn;Yi.prototype.setLineJoin=Yi.prototype.cp;Yi.prototype.setMiterLimit=Yi.prototype.ep;Yi.prototype.setWidth=Yi.prototype.ip;r("ol.style.Style",Zi);Zi.prototype.getGeometry=Zi.prototype.Y;Zi.prototype.getGeometryFunction=Zi.prototype.Wj;Zi.prototype.getFill=Zi.prototype.Kn;Zi.prototype.getImage=Zi.prototype.Ln;Zi.prototype.getStroke=Zi.prototype.Mn;Zi.prototype.getText=Zi.prototype.Ja;Zi.prototype.getZIndex=Zi.prototype.Nn;Zi.prototype.setGeometry=Zi.prototype.Gh;
Zi.prototype.setZIndex=Zi.prototype.On;r("ol.style.Text",Ro);Ro.prototype.getFont=Ro.prototype.Uj;Ro.prototype.getOffsetX=Ro.prototype.hk;Ro.prototype.getOffsetY=Ro.prototype.ik;Ro.prototype.getFill=Ro.prototype.Pn;Ro.prototype.getRotateWithView=Ro.prototype.Qn;Ro.prototype.getRotation=Ro.prototype.Rn;Ro.prototype.getScale=Ro.prototype.Sn;Ro.prototype.getStroke=Ro.prototype.Tn;Ro.prototype.getText=Ro.prototype.Ja;Ro.prototype.getTextAlign=Ro.prototype.vk;Ro.prototype.getTextBaseline=Ro.prototype.wk;
Ro.prototype.setFont=Ro.prototype.Zo;Ro.prototype.setOffsetX=Ro.prototype.ji;Ro.prototype.setOffsetY=Ro.prototype.ki;Ro.prototype.setFill=Ro.prototype.Yo;Ro.prototype.setRotation=Ro.prototype.Un;Ro.prototype.setScale=Ro.prototype.Vn;Ro.prototype.setStroke=Ro.prototype.fp;Ro.prototype.setText=Ro.prototype.mi;Ro.prototype.setTextAlign=Ro.prototype.ni;Ro.prototype.setTextBaseline=Ro.prototype.gp;r("ol.Sphere",pc);pc.prototype.geodesicArea=pc.prototype.a;pc.prototype.haversineDistance=pc.prototype.b;
r("ol.source.BingMaps",mv);r("ol.source.BingMaps.TOS_ATTRIBUTION",nv);r("ol.source.CartoDB",pv);pv.prototype.getConfig=pv.prototype.Qj;pv.prototype.updateConfig=pv.prototype.rp;pv.prototype.setConfig=pv.prototype.Xo;r("ol.source.Cluster",Y);Y.prototype.getSource=Y.prototype.sb;Y.prototype.setDistance=Y.prototype.tb;r("ol.source.Image",tk);vk.prototype.image=vk.prototype.image;r("ol.source.ImageArcGISRest",vv);vv.prototype.getParams=vv.prototype.Nm;vv.prototype.getImageLoadFunction=vv.prototype.Mm;
vv.prototype.getUrl=vv.prototype.Om;vv.prototype.setImageLoadFunction=vv.prototype.Pm;vv.prototype.setUrl=vv.prototype.Qm;vv.prototype.updateParams=vv.prototype.Rm;r("ol.source.ImageCanvas",Ak);r("ol.source.ImageMapGuide",wv);wv.prototype.getParams=wv.prototype.Tm;wv.prototype.getImageLoadFunction=wv.prototype.Sm;wv.prototype.updateParams=wv.prototype.Vm;wv.prototype.setImageLoadFunction=wv.prototype.Um;r("ol.source.ImageStatic",xv);r("ol.source.ImageVector",Bk);Bk.prototype.getSource=Bk.prototype.Wm;
Bk.prototype.getStyle=Bk.prototype.Xm;Bk.prototype.getStyleFunction=Bk.prototype.Ym;Bk.prototype.setStyle=Bk.prototype.uh;r("ol.source.ImageWMS",yv);yv.prototype.getGetFeatureInfoUrl=yv.prototype.an;yv.prototype.getParams=yv.prototype.cn;yv.prototype.getImageLoadFunction=yv.prototype.bn;yv.prototype.getUrl=yv.prototype.dn;yv.prototype.setImageLoadFunction=yv.prototype.en;yv.prototype.setUrl=yv.prototype.fn;yv.prototype.updateParams=yv.prototype.gn;r("ol.source.OSM",Cv);
r("ol.source.OSM.ATTRIBUTION",Dv);r("ol.source.Raster",Ev);Ev.prototype.setOperation=Ev.prototype.v;Jv.prototype.extent=Jv.prototype.extent;Jv.prototype.resolution=Jv.prototype.resolution;Jv.prototype.data=Jv.prototype.data;r("ol.source.Source",Tg);Tg.prototype.getAttributions=Tg.prototype.xa;Tg.prototype.getLogo=Tg.prototype.wa;Tg.prototype.getProjection=Tg.prototype.ya;Tg.prototype.getState=Tg.prototype.V;Tg.prototype.refresh=Tg.prototype.va;Tg.prototype.setAttributions=Tg.prototype.ra;
r("ol.source.Stamen",Ov);r("ol.source.Tile",ev);ev.prototype.getTileGrid=ev.prototype.Pa;hv.prototype.tile=hv.prototype.tile;r("ol.source.TileArcGISRest",Qv);Qv.prototype.getParams=Qv.prototype.v;Qv.prototype.updateParams=Qv.prototype.A;r("ol.source.TileDebug",Sv);r("ol.source.TileImage",W);W.prototype.setRenderReprojectionEdges=W.prototype.Db;W.prototype.setTileGridForProjection=W.prototype.Eb;r("ol.source.TileJSON",Tv);Tv.prototype.getTileJSON=Tv.prototype.xk;r("ol.source.TileUTFGrid",Uv);
Uv.prototype.getTemplate=Uv.prototype.uk;Uv.prototype.forDataAtCoordinateAndResolution=Uv.prototype.Fj;r("ol.source.TileWMS",Yv);Yv.prototype.getGetFeatureInfoUrl=Yv.prototype.pn;Yv.prototype.getParams=Yv.prototype.qn;Yv.prototype.updateParams=Yv.prototype.rn;iv.prototype.getTileLoadFunction=iv.prototype.gb;iv.prototype.getTileUrlFunction=iv.prototype.hb;iv.prototype.getUrls=iv.prototype.ib;iv.prototype.setTileLoadFunction=iv.prototype.mb;iv.prototype.setTileUrlFunction=iv.prototype.Ta;
iv.prototype.setUrl=iv.prototype.Za;iv.prototype.setUrls=iv.prototype.Ua;r("ol.source.Vector",U);U.prototype.addFeature=U.prototype.ub;U.prototype.addFeatures=U.prototype.Hc;U.prototype.clear=U.prototype.clear;U.prototype.forEachFeature=U.prototype.ug;U.prototype.forEachFeatureInExtent=U.prototype.xb;U.prototype.forEachFeatureIntersectingExtent=U.prototype.vg;U.prototype.getFeaturesCollection=U.prototype.Dg;U.prototype.getFeatures=U.prototype.me;U.prototype.getFeaturesAtCoordinate=U.prototype.Cg;
U.prototype.getFeaturesInExtent=U.prototype.cf;U.prototype.getClosestFeatureToCoordinate=U.prototype.yg;U.prototype.getExtent=U.prototype.C;U.prototype.getFeatureById=U.prototype.Bg;U.prototype.getFormat=U.prototype.zh;U.prototype.getUrl=U.prototype.Ah;U.prototype.removeFeature=U.prototype.pb;Zt.prototype.feature=Zt.prototype.feature;r("ol.source.VectorTile",dw);r("ol.source.WMTS",Z);Z.prototype.getDimensions=Z.prototype.Sj;Z.prototype.getFormat=Z.prototype.sn;Z.prototype.getLayer=Z.prototype.tn;
Z.prototype.getMatrixSet=Z.prototype.ek;Z.prototype.getRequestEncoding=Z.prototype.sk;Z.prototype.getStyle=Z.prototype.vn;Z.prototype.getVersion=Z.prototype.zk;Z.prototype.updateDimensions=Z.prototype.sp;
r("ol.source.WMTS.optionsFromCapabilities",function(a,b){var c=gb(a.Contents.Layer,function(a){return a.Identifier==b.layer}),d=a.Contents.TileMatrixSet,e,f;e=1<c.TileMatrixSetLink.length?"projection"in b?kb(c.TileMatrixSetLink,function(a){return gb(d,function(b){return b.Identifier==a.TileMatrixSet}).SupportedCRS.replace(/urn:ogc:def:crs:(\w+):(.*:)?(\w+)$/,"$1:$3")==b.projection}):kb(c.TileMatrixSetLink,function(a){return a.TileMatrixSet==b.matrixSet}):0;0>e&&(e=0);f=c.TileMatrixSetLink[e].TileMatrixSet;
var g=c.Format[0];"format"in b&&(g=b.format);e=kb(c.Style,function(a){return"style"in b?a.Title==b.style:a.isDefault});0>e&&(e=0);e=c.Style[e].Identifier;var h={};"Dimension"in c&&c.Dimension.forEach(function(a){var b=a.Identifier,c=a.Default;void 0===c&&(c=a.Value[0]);h[b]=c});var l=gb(a.Contents.TileMatrixSet,function(a){return a.Identifier==f}),m;m="projection"in b?vc(b.projection):vc(l.SupportedCRS.replace(/urn:ogc:def:crs:(\w+):(.*:)?(\w+)$/,"$1:$3"));var n=c.WGS84BoundingBox,p,q;void 0!==n&&
(q=vc("EPSG:4326").C(),q=n[0]==q[0]&&n[2]==q[2],p=Pc(n,"EPSG:4326",m),(n=m.C())&&(Nb(n,p)||(p=void 0)));var l=gw(l,p),t=[];p=b.requestEncoding;p=void 0!==p?p:"";if("OperationsMetadata"in a&&"GetTile"in a.OperationsMetadata)for(var n=a.OperationsMetadata.GetTile.DCP.HTTP.Get,v=0,u=n.length;v<u;++v){var y=gb(n[v].Constraint,function(a){return"GetEncoding"==a.name}).AllowedValues.Value;""===p&&(p=y[0]);if("KVP"===p)bb(y,"KVP")&&t.push(n[v].href);else break}0===t.length&&(p="REST",c.ResourceURL.forEach(function(a){"tile"===
a.resourceType&&(g=a.format,t.push(a.template))}));return{urls:t,layer:b.layer,matrixSet:f,format:g,projection:m,requestEncoding:p,tileGrid:l,style:e,dimensions:h,wrapX:q}});r("ol.source.XYZ",ov);r("ol.source.Zoomify",iw);Sg.prototype.vectorContext=Sg.prototype.vectorContext;Sg.prototype.frameState=Sg.prototype.frameState;Sg.prototype.context=Sg.prototype.context;Sg.prototype.glContext=Sg.prototype.glContext;Rq.prototype.get=Rq.prototype.get;Rq.prototype.getExtent=Rq.prototype.C;
Rq.prototype.getGeometry=Rq.prototype.Y;Rq.prototype.getProperties=Rq.prototype.Im;Rq.prototype.getType=Rq.prototype.X;r("ol.render.VectorContext",fj);Bl.prototype.setStyle=Bl.prototype.rd;Bl.prototype.drawGeometry=Bl.prototype.sc;Bl.prototype.drawFeature=Bl.prototype.We;gj.prototype.drawCircle=gj.prototype.Qd;gj.prototype.setStyle=gj.prototype.rd;gj.prototype.drawGeometry=gj.prototype.sc;gj.prototype.drawFeature=gj.prototype.We;r("ol.proj.common.add",Ii);r("ol.proj.METERS_PER_UNIT",rc);
r("ol.proj.Projection",sc);sc.prototype.getCode=sc.prototype.Pj;sc.prototype.getExtent=sc.prototype.C;sc.prototype.getUnits=sc.prototype.Ab;sc.prototype.getMetersPerUnit=sc.prototype.cc;sc.prototype.getWorldExtent=sc.prototype.Bk;sc.prototype.isGlobal=sc.prototype.kl;sc.prototype.setGlobal=sc.prototype.ap;sc.prototype.setExtent=sc.prototype.Hm;sc.prototype.setWorldExtent=sc.prototype.jp;sc.prototype.setGetPointResolution=sc.prototype.$o;sc.prototype.getPointResolution=sc.prototype.getPointResolution;
r("ol.proj.setProj4",function(a){uc=a});r("ol.proj.addEquivalentProjections",wc);r("ol.proj.addProjection",Ic);r("ol.proj.addCoordinateTransforms",xc);r("ol.proj.fromLonLat",function(a,b){return Oc(a,"EPSG:4326",void 0!==b?b:"EPSG:3857")});r("ol.proj.toLonLat",function(a,b){return Oc(a,void 0!==b?b:"EPSG:3857","EPSG:4326")});r("ol.proj.get",vc);r("ol.proj.equivalent",Lc);r("ol.proj.getTransform",Mc);r("ol.proj.transform",Oc);r("ol.proj.transformExtent",Pc);r("ol.layer.Base",Qg);
Qg.prototype.getExtent=Qg.prototype.C;Qg.prototype.getMaxResolution=Qg.prototype.Rb;Qg.prototype.getMinResolution=Qg.prototype.Sb;Qg.prototype.getOpacity=Qg.prototype.Tb;Qg.prototype.getVisible=Qg.prototype.Bb;Qg.prototype.getZIndex=Qg.prototype.Ub;Qg.prototype.setExtent=Qg.prototype.ic;Qg.prototype.setMaxResolution=Qg.prototype.oc;Qg.prototype.setMinResolution=Qg.prototype.pc;Qg.prototype.setOpacity=Qg.prototype.jc;Qg.prototype.setVisible=Qg.prototype.kc;Qg.prototype.setZIndex=Qg.prototype.lc;
r("ol.layer.Group",zi);zi.prototype.getLayers=zi.prototype.Rc;zi.prototype.setLayers=zi.prototype.lh;r("ol.layer.Heatmap",V);V.prototype.getBlur=V.prototype.xg;V.prototype.getGradient=V.prototype.Eg;V.prototype.getRadius=V.prototype.mh;V.prototype.setBlur=V.prototype.ai;V.prototype.setGradient=V.prototype.fi;V.prototype.setRadius=V.prototype.nh;r("ol.layer.Image",Ji);Ji.prototype.getSource=Ji.prototype.ga;r("ol.layer.Layer",Wg);Wg.prototype.getSource=Wg.prototype.ga;Wg.prototype.setMap=Wg.prototype.setMap;
Wg.prototype.setSource=Wg.prototype.Dc;r("ol.layer.Tile",Ki);Ki.prototype.getPreload=Ki.prototype.f;Ki.prototype.getSource=Ki.prototype.ga;Ki.prototype.setPreload=Ki.prototype.l;Ki.prototype.getUseInterimTilesOnError=Ki.prototype.c;Ki.prototype.setUseInterimTilesOnError=Ki.prototype.A;r("ol.layer.Vector",F);F.prototype.getSource=F.prototype.ga;F.prototype.getStyle=F.prototype.H;F.prototype.getStyleFunction=F.prototype.D;F.prototype.setStyle=F.prototype.l;r("ol.layer.VectorTile",H);
H.prototype.getPreload=H.prototype.f;H.prototype.getUseInterimTilesOnError=H.prototype.c;H.prototype.setPreload=H.prototype.W;H.prototype.setUseInterimTilesOnError=H.prototype.ha;r("ol.interaction.DoubleClickZoom",Fh);r("ol.interaction.DoubleClickZoom.handleEvent",Gh);r("ol.interaction.DragAndDrop",Ft);r("ol.interaction.DragAndDrop.handleEvent",nc);It.prototype.features=It.prototype.features;It.prototype.file=It.prototype.file;It.prototype.projection=It.prototype.projection;
di.prototype.coordinate=di.prototype.coordinate;di.prototype.mapBrowserEvent=di.prototype.mapBrowserEvent;r("ol.interaction.DragBox",ei);ei.prototype.getGeometry=ei.prototype.Y;r("ol.interaction.DragPan",Th);r("ol.interaction.DragRotate",Xh);r("ol.interaction.DragRotateAndZoom",Kt);r("ol.interaction.DragZoom",ji);bu.prototype.feature=bu.prototype.feature;r("ol.interaction.Draw",cu);r("ol.interaction.Draw.handleEvent",eu);cu.prototype.removeLastPoint=cu.prototype.No;cu.prototype.finishDrawing=cu.prototype.jd;
cu.prototype.extend=cu.prototype.mm;r("ol.interaction.Draw.createRegularPolygon",function(a,b){return function(c,d){var e=c[0],f=c[1],g=Math.sqrt(Ab(e,f)),h=d?d:zd(new st(e),a);Ad(h,e,g,b?b:Math.atan((f[1]-e[1])/(f[0]-e[0])));return h}});r("ol.interaction.defaults",yi);r("ol.interaction.Interaction",Bh);Bh.prototype.getActive=Bh.prototype.f;Bh.prototype.getMap=Bh.prototype.l;Bh.prototype.setActive=Bh.prototype.i;r("ol.interaction.KeyboardPan",ki);r("ol.interaction.KeyboardPan.handleEvent",li);
r("ol.interaction.KeyboardZoom",mi);r("ol.interaction.KeyboardZoom.handleEvent",ni);su.prototype.features=su.prototype.features;su.prototype.mapBrowserEvent=su.prototype.mapBrowserEvent;r("ol.interaction.Modify",tu);r("ol.interaction.Modify.handleEvent",wu);tu.prototype.removePoint=tu.prototype.Yh;r("ol.interaction.MouseWheelZoom",oi);r("ol.interaction.MouseWheelZoom.handleEvent",pi);oi.prototype.setMouseAnchor=oi.prototype.D;r("ol.interaction.PinchRotate",qi);r("ol.interaction.PinchZoom",ui);
r("ol.interaction.Pointer",Qh);r("ol.interaction.Pointer.handleEvent",Rh);Gu.prototype.selected=Gu.prototype.selected;Gu.prototype.deselected=Gu.prototype.deselected;Gu.prototype.mapBrowserEvent=Gu.prototype.mapBrowserEvent;r("ol.interaction.Select",Hu);Hu.prototype.getFeatures=Hu.prototype.wm;Hu.prototype.getLayer=Hu.prototype.xm;r("ol.interaction.Select.handleEvent",Iu);Hu.prototype.setMap=Hu.prototype.setMap;r("ol.interaction.Snap",Ku);Ku.prototype.addFeature=Ku.prototype.ub;
Ku.prototype.removeFeature=Ku.prototype.pb;Ou.prototype.features=Ou.prototype.features;Ou.prototype.coordinate=Ou.prototype.coordinate;r("ol.interaction.Translate",Pu);r("ol.geom.Circle",st);st.prototype.clone=st.prototype.clone;st.prototype.getCenter=st.prototype.qd;st.prototype.getRadius=st.prototype.vf;st.prototype.getType=st.prototype.X;st.prototype.intersectsExtent=st.prototype.La;st.prototype.setCenter=st.prototype.em;st.prototype.setCenterAndRadius=st.prototype.Uf;st.prototype.setRadius=st.prototype.fm;
st.prototype.transform=st.prototype.lb;r("ol.geom.Geometry",Qc);Qc.prototype.getClosestPoint=Qc.prototype.yb;Qc.prototype.intersectsCoordinate=Qc.prototype.jb;Qc.prototype.getExtent=Qc.prototype.C;Qc.prototype.rotate=Qc.prototype.rotate;Qc.prototype.scale=Qc.prototype.scale;Qc.prototype.simplify=Qc.prototype.Fb;Qc.prototype.transform=Qc.prototype.lb;r("ol.geom.GeometryCollection",qn);qn.prototype.clone=qn.prototype.clone;qn.prototype.getGeometries=qn.prototype.df;qn.prototype.getType=qn.prototype.X;
qn.prototype.intersectsExtent=qn.prototype.La;qn.prototype.setGeometries=qn.prototype.ei;qn.prototype.applyTransform=qn.prototype.rc;qn.prototype.translate=qn.prototype.Qc;r("ol.geom.LinearRing",jd);jd.prototype.clone=jd.prototype.clone;jd.prototype.getArea=jd.prototype.im;jd.prototype.getCoordinates=jd.prototype.Z;jd.prototype.getType=jd.prototype.X;jd.prototype.setCoordinates=jd.prototype.sa;r("ol.geom.LineString",P);P.prototype.appendCoordinate=P.prototype.sj;P.prototype.clone=P.prototype.clone;
P.prototype.forEachSegment=P.prototype.Ij;P.prototype.getCoordinateAtM=P.prototype.gm;P.prototype.getCoordinates=P.prototype.Z;P.prototype.getCoordinateAt=P.prototype.zg;P.prototype.getLength=P.prototype.hm;P.prototype.getType=P.prototype.X;P.prototype.intersectsExtent=P.prototype.La;P.prototype.setCoordinates=P.prototype.sa;r("ol.geom.MultiLineString",Q);Q.prototype.appendLineString=Q.prototype.tj;Q.prototype.clone=Q.prototype.clone;Q.prototype.getCoordinateAtM=Q.prototype.jm;
Q.prototype.getCoordinates=Q.prototype.Z;Q.prototype.getLineString=Q.prototype.ck;Q.prototype.getLineStrings=Q.prototype.ld;Q.prototype.getType=Q.prototype.X;Q.prototype.intersectsExtent=Q.prototype.La;Q.prototype.setCoordinates=Q.prototype.sa;r("ol.geom.MultiPoint",R);R.prototype.appendPoint=R.prototype.vj;R.prototype.clone=R.prototype.clone;R.prototype.getCoordinates=R.prototype.Z;R.prototype.getPoint=R.prototype.nk;R.prototype.getPoints=R.prototype.he;R.prototype.getType=R.prototype.X;
R.prototype.intersectsExtent=R.prototype.La;R.prototype.setCoordinates=R.prototype.sa;r("ol.geom.MultiPolygon",S);S.prototype.appendPolygon=S.prototype.wj;S.prototype.clone=S.prototype.clone;S.prototype.getArea=S.prototype.km;S.prototype.getCoordinates=S.prototype.Z;S.prototype.getInteriorPoints=S.prototype.$j;S.prototype.getPolygon=S.prototype.qk;S.prototype.getPolygons=S.prototype.Wd;S.prototype.getType=S.prototype.X;S.prototype.intersectsExtent=S.prototype.La;S.prototype.setCoordinates=S.prototype.sa;
r("ol.geom.Point",C);C.prototype.clone=C.prototype.clone;C.prototype.getCoordinates=C.prototype.Z;C.prototype.getType=C.prototype.X;C.prototype.intersectsExtent=C.prototype.La;C.prototype.setCoordinates=C.prototype.sa;r("ol.geom.Polygon",D);D.prototype.appendLinearRing=D.prototype.uj;D.prototype.clone=D.prototype.clone;D.prototype.getArea=D.prototype.lm;D.prototype.getCoordinates=D.prototype.Z;D.prototype.getInteriorPoint=D.prototype.Zj;D.prototype.getLinearRingCount=D.prototype.dk;
D.prototype.getLinearRing=D.prototype.Fg;D.prototype.getLinearRings=D.prototype.Vd;D.prototype.getType=D.prototype.X;D.prototype.intersectsExtent=D.prototype.La;D.prototype.setCoordinates=D.prototype.sa;r("ol.geom.Polygon.circular",xd);r("ol.geom.Polygon.fromExtent",yd);r("ol.geom.Polygon.fromCircle",zd);r("ol.geom.SimpleGeometry",Sc);Sc.prototype.getFirstCoordinate=Sc.prototype.Nb;Sc.prototype.getLastCoordinate=Sc.prototype.Ob;Sc.prototype.getLayout=Sc.prototype.Pb;Sc.prototype.applyTransform=Sc.prototype.rc;
Sc.prototype.translate=Sc.prototype.Qc;r("ol.format.EsriJSON",jn);jn.prototype.readFeature=jn.prototype.Vb;jn.prototype.readFeatures=jn.prototype.Fa;jn.prototype.readGeometry=jn.prototype.Uc;jn.prototype.readProjection=jn.prototype.Ra;jn.prototype.writeGeometry=jn.prototype.Yc;jn.prototype.writeGeometryObject=jn.prototype.Ie;jn.prototype.writeFeature=jn.prototype.Cd;jn.prototype.writeFeatureObject=jn.prototype.Xc;jn.prototype.writeFeatures=jn.prototype.ac;jn.prototype.writeFeaturesObject=jn.prototype.He;
r("ol.format.Feature",Xm);r("ol.format.GeoJSON",un);un.prototype.readFeature=un.prototype.Vb;un.prototype.readFeatures=un.prototype.Fa;un.prototype.readGeometry=un.prototype.Uc;un.prototype.readProjection=un.prototype.Ra;un.prototype.writeFeature=un.prototype.Cd;un.prototype.writeFeatureObject=un.prototype.Xc;un.prototype.writeFeatures=un.prototype.ac;un.prototype.writeFeaturesObject=un.prototype.He;un.prototype.writeGeometry=un.prototype.Yc;un.prototype.writeGeometryObject=un.prototype.Ie;
r("ol.format.GML",Pn);Pn.prototype.writeFeatures=Pn.prototype.ac;Pn.prototype.writeFeaturesNode=Pn.prototype.a;r("ol.format.GML2",Yn);r("ol.format.GML3",Pn);Pn.prototype.writeGeometryNode=Pn.prototype.s;Pn.prototype.writeFeatures=Pn.prototype.ac;Pn.prototype.writeFeaturesNode=Pn.prototype.a;Cn.prototype.readFeatures=Cn.prototype.Fa;r("ol.format.GPX",Zn);Zn.prototype.readFeature=Zn.prototype.Vb;Zn.prototype.readFeatures=Zn.prototype.Fa;Zn.prototype.readProjection=Zn.prototype.Ra;
Zn.prototype.writeFeatures=Zn.prototype.ac;Zn.prototype.writeFeaturesNode=Zn.prototype.a;r("ol.format.IGC",Jo);Jo.prototype.readFeature=Jo.prototype.Vb;Jo.prototype.readFeatures=Jo.prototype.Fa;Jo.prototype.readProjection=Jo.prototype.Ra;r("ol.format.KML",So);So.prototype.readFeature=So.prototype.Vb;So.prototype.readFeatures=So.prototype.Fa;So.prototype.readName=So.prototype.Co;So.prototype.readNetworkLinks=So.prototype.Do;So.prototype.readProjection=So.prototype.Ra;So.prototype.writeFeatures=So.prototype.ac;
So.prototype.writeFeaturesNode=So.prototype.a;r("ol.format.MVT",Sq);Sq.prototype.readFeatures=Sq.prototype.Fa;Sq.prototype.readProjection=Sq.prototype.Ra;Sq.prototype.setLayers=Sq.prototype.c;r("ol.format.OSMXML",qr);qr.prototype.readFeatures=qr.prototype.Fa;qr.prototype.readProjection=qr.prototype.Ra;r("ol.format.Polyline",Pr);r("ol.format.Polyline.encodeDeltas",Qr);r("ol.format.Polyline.decodeDeltas",Sr);r("ol.format.Polyline.encodeFloats",Rr);r("ol.format.Polyline.decodeFloats",Tr);
Pr.prototype.readFeature=Pr.prototype.Vb;Pr.prototype.readFeatures=Pr.prototype.Fa;Pr.prototype.readGeometry=Pr.prototype.Uc;Pr.prototype.readProjection=Pr.prototype.Ra;Pr.prototype.writeGeometry=Pr.prototype.Yc;r("ol.format.TopoJSON",Ur);Ur.prototype.readFeatures=Ur.prototype.Fa;Ur.prototype.readProjection=Ur.prototype.Ra;r("ol.format.WFS",$r);$r.prototype.readFeatures=$r.prototype.Fa;$r.prototype.readTransactionResponse=$r.prototype.j;$r.prototype.readFeatureCollectionMetadata=$r.prototype.l;
$r.prototype.writeGetFeature=$r.prototype.o;$r.prototype.writeTransaction=$r.prototype.U;$r.prototype.readProjection=$r.prototype.Ra;r("ol.format.WKT",qs);qs.prototype.readFeature=qs.prototype.Vb;qs.prototype.readFeatures=qs.prototype.Fa;qs.prototype.readGeometry=qs.prototype.Uc;qs.prototype.writeFeature=qs.prototype.Cd;qs.prototype.writeFeatures=qs.prototype.ac;qs.prototype.writeGeometry=qs.prototype.Yc;r("ol.format.WMSCapabilities",Hs);Hs.prototype.read=Hs.prototype.read;
r("ol.format.WMSGetFeatureInfo",dt);dt.prototype.readFeatures=dt.prototype.Fa;r("ol.format.WMTSCapabilities",et);et.prototype.read=et.prototype.read;r("ol.format.ogc.filter.And",Xq);r("ol.format.ogc.filter.Bbox",Yq);r("ol.format.ogc.filter.Comparison",Zq);r("ol.format.ogc.filter.ComparisonBinary",$q);r("ol.format.ogc.filter.EqualTo",ar);r("ol.format.ogc.filter.Filter",Uq);r("ol.format.ogc.filter.GreaterThan",br);r("ol.format.ogc.filter.GreaterThanOrEqualTo",cr);r("ol.format.ogc.filter.and",or);
r("ol.format.ogc.filter.or",function(a,b){return new mr(a,b)});r("ol.format.ogc.filter.not",function(a){return new kr(a)});r("ol.format.ogc.filter.bbox",pr);r("ol.format.ogc.filter.intersects",function(a,b,c){return new er(a,b,c)});r("ol.format.ogc.filter.within",function(a,b,c){return new nr(a,b,c)});r("ol.format.ogc.filter.equalTo",function(a,b,c){return new ar(a,b,c)});r("ol.format.ogc.filter.notEqualTo",function(a,b,c){return new lr(a,b,c)});
r("ol.format.ogc.filter.lessThan",function(a,b){return new ir(a,b)});r("ol.format.ogc.filter.lessThanOrEqualTo",function(a,b){return new jr(a,b)});r("ol.format.ogc.filter.greaterThan",function(a,b){return new br(a,b)});r("ol.format.ogc.filter.greaterThanOrEqualTo",function(a,b){return new cr(a,b)});r("ol.format.ogc.filter.isNull",function(a){return new hr(a)});r("ol.format.ogc.filter.between",function(a,b,c){return new fr(a,b,c)});
r("ol.format.ogc.filter.like",function(a,b,c,d,e,f){return new gr(a,b,c,d,e,f)});r("ol.format.ogc.filter.Intersects",er);r("ol.format.ogc.filter.IsBetween",fr);r("ol.format.ogc.filter.IsLike",gr);r("ol.format.ogc.filter.IsNull",hr);r("ol.format.ogc.filter.LessThan",ir);r("ol.format.ogc.filter.LessThanOrEqualTo",jr);r("ol.format.ogc.filter.Not",kr);r("ol.format.ogc.filter.NotEqualTo",lr);r("ol.format.ogc.filter.Or",mr);r("ol.format.ogc.filter.Spatial",dr);r("ol.format.ogc.filter.Within",nr);
r("ol.events.condition.altKeyOnly",function(a){a=a.originalEvent;return a.altKey&&!(a.metaKey||a.ctrlKey)&&!a.shiftKey});r("ol.events.condition.altShiftKeysOnly",Hh);r("ol.events.condition.always",nc);r("ol.events.condition.click",function(a){return a.type==Gg});r("ol.events.condition.never",oc);r("ol.events.condition.pointerMove",Jh);r("ol.events.condition.singleClick",Kh);r("ol.events.condition.doubleClick",function(a){return a.type==Hg});r("ol.events.condition.noModifierKeys",Lh);
r("ol.events.condition.platformModifierKeyOnly",function(a){a=a.originalEvent;return!a.altKey&&(Jf?a.metaKey:a.ctrlKey)&&!a.shiftKey});r("ol.events.condition.shiftKeyOnly",Mh);r("ol.events.condition.targetNotEditable",Nh);r("ol.events.condition.mouseOnly",Oh);r("ol.events.condition.primaryAction",Ph);Pa.prototype.type=Pa.prototype.type;Pa.prototype.target=Pa.prototype.target;Pa.prototype.preventDefault=Pa.prototype.preventDefault;Pa.prototype.stopPropagation=Pa.prototype.stopPropagation;
r("ol.control.Attribution",kf);r("ol.control.Attribution.render",lf);kf.prototype.getCollapsible=kf.prototype.Vl;kf.prototype.setCollapsible=kf.prototype.Yl;kf.prototype.setCollapsed=kf.prototype.Xl;kf.prototype.getCollapsed=kf.prototype.Ul;r("ol.control.Control",jf);jf.prototype.getMap=jf.prototype.i;jf.prototype.setMap=jf.prototype.setMap;jf.prototype.setTarget=jf.prototype.c;r("ol.control.FullScreen",nf);r("ol.control.defaults",wf);r("ol.control.MousePosition",xf);
r("ol.control.MousePosition.render",yf);xf.prototype.getCoordinateFormat=xf.prototype.Ag;xf.prototype.getProjection=xf.prototype.eh;xf.prototype.setCoordinateFormat=xf.prototype.bi;xf.prototype.setProjection=xf.prototype.fh;r("ol.control.OverviewMap",em);r("ol.control.OverviewMap.render",fm);em.prototype.getCollapsible=em.prototype.am;em.prototype.setCollapsible=em.prototype.dm;em.prototype.setCollapsed=em.prototype.cm;em.prototype.getCollapsed=em.prototype.$l;em.prototype.getOverviewMap=em.prototype.lk;
r("ol.control.Rotate",sf);r("ol.control.Rotate.render",uf);r("ol.control.ScaleLine",jm);jm.prototype.getUnits=jm.prototype.Ab;r("ol.control.ScaleLine.render",km);jm.prototype.setUnits=jm.prototype.D;r("ol.control.Zoom",vf);r("ol.control.ZoomSlider",tm);r("ol.control.ZoomSlider.render",vm);r("ol.control.ZoomToExtent",ym);Xa.prototype.changed=Xa.prototype.u;Xa.prototype.dispatchEvent=Xa.prototype.b;Xa.prototype.getRevision=Xa.prototype.K;Xa.prototype.on=Xa.prototype.I;Xa.prototype.once=Xa.prototype.L;
Xa.prototype.un=Xa.prototype.J;Xa.prototype.unByKey=Xa.prototype.M;re.prototype.get=re.prototype.get;re.prototype.getKeys=re.prototype.P;re.prototype.getProperties=re.prototype.N;re.prototype.set=re.prototype.set;re.prototype.setProperties=re.prototype.G;re.prototype.unset=re.prototype.S;re.prototype.changed=re.prototype.u;re.prototype.dispatchEvent=re.prototype.b;re.prototype.getRevision=re.prototype.K;re.prototype.on=re.prototype.I;re.prototype.once=re.prototype.L;re.prototype.un=re.prototype.J;
re.prototype.unByKey=re.prototype.M;te.prototype.type=te.prototype.type;te.prototype.target=te.prototype.target;te.prototype.preventDefault=te.prototype.preventDefault;te.prototype.stopPropagation=te.prototype.stopPropagation;zm.prototype.get=zm.prototype.get;zm.prototype.getKeys=zm.prototype.P;zm.prototype.getProperties=zm.prototype.N;zm.prototype.set=zm.prototype.set;zm.prototype.setProperties=zm.prototype.G;zm.prototype.unset=zm.prototype.S;zm.prototype.changed=zm.prototype.u;
zm.prototype.dispatchEvent=zm.prototype.b;zm.prototype.getRevision=zm.prototype.K;zm.prototype.on=zm.prototype.I;zm.prototype.once=zm.prototype.L;zm.prototype.un=zm.prototype.J;zm.prototype.unByKey=zm.prototype.M;Am.prototype.get=Am.prototype.get;Am.prototype.getKeys=Am.prototype.P;Am.prototype.getProperties=Am.prototype.N;Am.prototype.set=Am.prototype.set;Am.prototype.setProperties=Am.prototype.G;Am.prototype.unset=Am.prototype.S;Am.prototype.changed=Am.prototype.u;Am.prototype.dispatchEvent=Am.prototype.b;
Am.prototype.getRevision=Am.prototype.K;Am.prototype.on=Am.prototype.I;Am.prototype.once=Am.prototype.L;Am.prototype.un=Am.prototype.J;Am.prototype.unByKey=Am.prototype.M;rt.prototype.get=rt.prototype.get;rt.prototype.getKeys=rt.prototype.P;rt.prototype.getProperties=rt.prototype.N;rt.prototype.set=rt.prototype.set;rt.prototype.setProperties=rt.prototype.G;rt.prototype.unset=rt.prototype.S;rt.prototype.changed=rt.prototype.u;rt.prototype.dispatchEvent=rt.prototype.b;rt.prototype.getRevision=rt.prototype.K;
rt.prototype.on=rt.prototype.I;rt.prototype.once=rt.prototype.L;rt.prototype.un=rt.prototype.J;rt.prototype.unByKey=rt.prototype.M;Dt.prototype.getTileCoord=Dt.prototype.i;I.prototype.get=I.prototype.get;I.prototype.getKeys=I.prototype.P;I.prototype.getProperties=I.prototype.N;I.prototype.set=I.prototype.set;I.prototype.setProperties=I.prototype.G;I.prototype.unset=I.prototype.S;I.prototype.changed=I.prototype.u;I.prototype.dispatchEvent=I.prototype.b;I.prototype.getRevision=I.prototype.K;
I.prototype.on=I.prototype.I;I.prototype.once=I.prototype.L;I.prototype.un=I.prototype.J;I.prototype.unByKey=I.prototype.M;hf.prototype.type=hf.prototype.type;hf.prototype.target=hf.prototype.target;hf.prototype.preventDefault=hf.prototype.preventDefault;hf.prototype.stopPropagation=hf.prototype.stopPropagation;Cg.prototype.map=Cg.prototype.map;Cg.prototype.frameState=Cg.prototype.frameState;Cg.prototype.type=Cg.prototype.type;Cg.prototype.target=Cg.prototype.target;Cg.prototype.preventDefault=Cg.prototype.preventDefault;
Cg.prototype.stopPropagation=Cg.prototype.stopPropagation;Dg.prototype.originalEvent=Dg.prototype.originalEvent;Dg.prototype.pixel=Dg.prototype.pixel;Dg.prototype.coordinate=Dg.prototype.coordinate;Dg.prototype.dragging=Dg.prototype.dragging;Dg.prototype.preventDefault=Dg.prototype.preventDefault;Dg.prototype.stopPropagation=Dg.prototype.stopPropagation;Dg.prototype.map=Dg.prototype.map;Dg.prototype.frameState=Dg.prototype.frameState;Dg.prototype.type=Dg.prototype.type;Dg.prototype.target=Dg.prototype.target;
Wa.prototype.type=Wa.prototype.type;Wa.prototype.target=Wa.prototype.target;Wa.prototype.preventDefault=Wa.prototype.preventDefault;Wa.prototype.stopPropagation=Wa.prototype.stopPropagation;am.prototype.get=am.prototype.get;am.prototype.getKeys=am.prototype.P;am.prototype.getProperties=am.prototype.N;am.prototype.set=am.prototype.set;am.prototype.setProperties=am.prototype.G;am.prototype.unset=am.prototype.S;am.prototype.changed=am.prototype.u;am.prototype.dispatchEvent=am.prototype.b;
am.prototype.getRevision=am.prototype.K;am.prototype.on=am.prototype.I;am.prototype.once=am.prototype.L;am.prototype.un=am.prototype.J;am.prototype.unByKey=am.prototype.M;cw.prototype.getTileCoord=cw.prototype.i;Bd.prototype.get=Bd.prototype.get;Bd.prototype.getKeys=Bd.prototype.P;Bd.prototype.getProperties=Bd.prototype.N;Bd.prototype.set=Bd.prototype.set;Bd.prototype.setProperties=Bd.prototype.G;Bd.prototype.unset=Bd.prototype.S;Bd.prototype.changed=Bd.prototype.u;Bd.prototype.dispatchEvent=Bd.prototype.b;
Bd.prototype.getRevision=Bd.prototype.K;Bd.prototype.on=Bd.prototype.I;Bd.prototype.once=Bd.prototype.L;Bd.prototype.un=Bd.prototype.J;Bd.prototype.unByKey=Bd.prototype.M;fw.prototype.forEachTileCoord=fw.prototype.wg;fw.prototype.getMaxZoom=fw.prototype.Gg;fw.prototype.getMinZoom=fw.prototype.Hg;fw.prototype.getOrigin=fw.prototype.Ka;fw.prototype.getResolution=fw.prototype.$;fw.prototype.getResolutions=fw.prototype.Hh;fw.prototype.getTileCoordExtent=fw.prototype.Ea;
fw.prototype.getTileCoordForCoordAndResolution=fw.prototype.Yd;fw.prototype.getTileCoordForCoordAndZ=fw.prototype.pd;fw.prototype.getTileSize=fw.prototype.Qa;fw.prototype.getZForResolution=fw.prototype.ec;Qi.prototype.getOpacity=Qi.prototype.oe;Qi.prototype.getRotateWithView=Qi.prototype.pe;Qi.prototype.getRotation=Qi.prototype.qe;Qi.prototype.getScale=Qi.prototype.re;Qi.prototype.getSnapToPixel=Qi.prototype.Xd;Qi.prototype.setOpacity=Qi.prototype.se;Qi.prototype.setRotation=Qi.prototype.te;
Qi.prototype.setScale=Qi.prototype.ue;Qo.prototype.getOpacity=Qo.prototype.oe;Qo.prototype.getRotateWithView=Qo.prototype.pe;Qo.prototype.getRotation=Qo.prototype.qe;Qo.prototype.getScale=Qo.prototype.re;Qo.prototype.getSnapToPixel=Qo.prototype.Xd;Qo.prototype.setOpacity=Qo.prototype.se;Qo.prototype.setRotation=Qo.prototype.te;Qo.prototype.setScale=Qo.prototype.ue;ow.prototype.getOpacity=ow.prototype.oe;ow.prototype.getRotateWithView=ow.prototype.pe;ow.prototype.getRotation=ow.prototype.qe;
ow.prototype.getScale=ow.prototype.re;ow.prototype.getSnapToPixel=ow.prototype.Xd;ow.prototype.setOpacity=ow.prototype.se;ow.prototype.setRotation=ow.prototype.te;ow.prototype.setScale=ow.prototype.ue;Tg.prototype.get=Tg.prototype.get;Tg.prototype.getKeys=Tg.prototype.P;Tg.prototype.getProperties=Tg.prototype.N;Tg.prototype.set=Tg.prototype.set;Tg.prototype.setProperties=Tg.prototype.G;Tg.prototype.unset=Tg.prototype.S;Tg.prototype.changed=Tg.prototype.u;Tg.prototype.dispatchEvent=Tg.prototype.b;
Tg.prototype.getRevision=Tg.prototype.K;Tg.prototype.on=Tg.prototype.I;Tg.prototype.once=Tg.prototype.L;Tg.prototype.un=Tg.prototype.J;Tg.prototype.unByKey=Tg.prototype.M;ev.prototype.getAttributions=ev.prototype.xa;ev.prototype.getLogo=ev.prototype.wa;ev.prototype.getProjection=ev.prototype.ya;ev.prototype.getState=ev.prototype.V;ev.prototype.refresh=ev.prototype.va;ev.prototype.setAttributions=ev.prototype.ra;ev.prototype.get=ev.prototype.get;ev.prototype.getKeys=ev.prototype.P;
ev.prototype.getProperties=ev.prototype.N;ev.prototype.set=ev.prototype.set;ev.prototype.setProperties=ev.prototype.G;ev.prototype.unset=ev.prototype.S;ev.prototype.changed=ev.prototype.u;ev.prototype.dispatchEvent=ev.prototype.b;ev.prototype.getRevision=ev.prototype.K;ev.prototype.on=ev.prototype.I;ev.prototype.once=ev.prototype.L;ev.prototype.un=ev.prototype.J;ev.prototype.unByKey=ev.prototype.M;iv.prototype.getTileGrid=iv.prototype.Pa;iv.prototype.refresh=iv.prototype.va;
iv.prototype.getAttributions=iv.prototype.xa;iv.prototype.getLogo=iv.prototype.wa;iv.prototype.getProjection=iv.prototype.ya;iv.prototype.getState=iv.prototype.V;iv.prototype.setAttributions=iv.prototype.ra;iv.prototype.get=iv.prototype.get;iv.prototype.getKeys=iv.prototype.P;iv.prototype.getProperties=iv.prototype.N;iv.prototype.set=iv.prototype.set;iv.prototype.setProperties=iv.prototype.G;iv.prototype.unset=iv.prototype.S;iv.prototype.changed=iv.prototype.u;iv.prototype.dispatchEvent=iv.prototype.b;
iv.prototype.getRevision=iv.prototype.K;iv.prototype.on=iv.prototype.I;iv.prototype.once=iv.prototype.L;iv.prototype.un=iv.prototype.J;iv.prototype.unByKey=iv.prototype.M;W.prototype.getTileLoadFunction=W.prototype.gb;W.prototype.getTileUrlFunction=W.prototype.hb;W.prototype.getUrls=W.prototype.ib;W.prototype.setTileLoadFunction=W.prototype.mb;W.prototype.setTileUrlFunction=W.prototype.Ta;W.prototype.setUrl=W.prototype.Za;W.prototype.setUrls=W.prototype.Ua;W.prototype.getTileGrid=W.prototype.Pa;
W.prototype.refresh=W.prototype.va;W.prototype.getAttributions=W.prototype.xa;W.prototype.getLogo=W.prototype.wa;W.prototype.getProjection=W.prototype.ya;W.prototype.getState=W.prototype.V;W.prototype.setAttributions=W.prototype.ra;W.prototype.get=W.prototype.get;W.prototype.getKeys=W.prototype.P;W.prototype.getProperties=W.prototype.N;W.prototype.set=W.prototype.set;W.prototype.setProperties=W.prototype.G;W.prototype.unset=W.prototype.S;W.prototype.changed=W.prototype.u;
W.prototype.dispatchEvent=W.prototype.b;W.prototype.getRevision=W.prototype.K;W.prototype.on=W.prototype.I;W.prototype.once=W.prototype.L;W.prototype.un=W.prototype.J;W.prototype.unByKey=W.prototype.M;mv.prototype.setRenderReprojectionEdges=mv.prototype.Db;mv.prototype.setTileGridForProjection=mv.prototype.Eb;mv.prototype.getTileLoadFunction=mv.prototype.gb;mv.prototype.getTileUrlFunction=mv.prototype.hb;mv.prototype.getUrls=mv.prototype.ib;mv.prototype.setTileLoadFunction=mv.prototype.mb;
mv.prototype.setTileUrlFunction=mv.prototype.Ta;mv.prototype.setUrl=mv.prototype.Za;mv.prototype.setUrls=mv.prototype.Ua;mv.prototype.getTileGrid=mv.prototype.Pa;mv.prototype.refresh=mv.prototype.va;mv.prototype.getAttributions=mv.prototype.xa;mv.prototype.getLogo=mv.prototype.wa;mv.prototype.getProjection=mv.prototype.ya;mv.prototype.getState=mv.prototype.V;mv.prototype.setAttributions=mv.prototype.ra;mv.prototype.get=mv.prototype.get;mv.prototype.getKeys=mv.prototype.P;
mv.prototype.getProperties=mv.prototype.N;mv.prototype.set=mv.prototype.set;mv.prototype.setProperties=mv.prototype.G;mv.prototype.unset=mv.prototype.S;mv.prototype.changed=mv.prototype.u;mv.prototype.dispatchEvent=mv.prototype.b;mv.prototype.getRevision=mv.prototype.K;mv.prototype.on=mv.prototype.I;mv.prototype.once=mv.prototype.L;mv.prototype.un=mv.prototype.J;mv.prototype.unByKey=mv.prototype.M;ov.prototype.setRenderReprojectionEdges=ov.prototype.Db;ov.prototype.setTileGridForProjection=ov.prototype.Eb;
ov.prototype.getTileLoadFunction=ov.prototype.gb;ov.prototype.getTileUrlFunction=ov.prototype.hb;ov.prototype.getUrls=ov.prototype.ib;ov.prototype.setTileLoadFunction=ov.prototype.mb;ov.prototype.setTileUrlFunction=ov.prototype.Ta;ov.prototype.setUrl=ov.prototype.Za;ov.prototype.setUrls=ov.prototype.Ua;ov.prototype.getTileGrid=ov.prototype.Pa;ov.prototype.refresh=ov.prototype.va;ov.prototype.getAttributions=ov.prototype.xa;ov.prototype.getLogo=ov.prototype.wa;ov.prototype.getProjection=ov.prototype.ya;
ov.prototype.getState=ov.prototype.V;ov.prototype.setAttributions=ov.prototype.ra;ov.prototype.get=ov.prototype.get;ov.prototype.getKeys=ov.prototype.P;ov.prototype.getProperties=ov.prototype.N;ov.prototype.set=ov.prototype.set;ov.prototype.setProperties=ov.prototype.G;ov.prototype.unset=ov.prototype.S;ov.prototype.changed=ov.prototype.u;ov.prototype.dispatchEvent=ov.prototype.b;ov.prototype.getRevision=ov.prototype.K;ov.prototype.on=ov.prototype.I;ov.prototype.once=ov.prototype.L;
ov.prototype.un=ov.prototype.J;ov.prototype.unByKey=ov.prototype.M;pv.prototype.setRenderReprojectionEdges=pv.prototype.Db;pv.prototype.setTileGridForProjection=pv.prototype.Eb;pv.prototype.getTileLoadFunction=pv.prototype.gb;pv.prototype.getTileUrlFunction=pv.prototype.hb;pv.prototype.getUrls=pv.prototype.ib;pv.prototype.setTileLoadFunction=pv.prototype.mb;pv.prototype.setTileUrlFunction=pv.prototype.Ta;pv.prototype.setUrl=pv.prototype.Za;pv.prototype.setUrls=pv.prototype.Ua;
pv.prototype.getTileGrid=pv.prototype.Pa;pv.prototype.refresh=pv.prototype.va;pv.prototype.getAttributions=pv.prototype.xa;pv.prototype.getLogo=pv.prototype.wa;pv.prototype.getProjection=pv.prototype.ya;pv.prototype.getState=pv.prototype.V;pv.prototype.setAttributions=pv.prototype.ra;pv.prototype.get=pv.prototype.get;pv.prototype.getKeys=pv.prototype.P;pv.prototype.getProperties=pv.prototype.N;pv.prototype.set=pv.prototype.set;pv.prototype.setProperties=pv.prototype.G;pv.prototype.unset=pv.prototype.S;
pv.prototype.changed=pv.prototype.u;pv.prototype.dispatchEvent=pv.prototype.b;pv.prototype.getRevision=pv.prototype.K;pv.prototype.on=pv.prototype.I;pv.prototype.once=pv.prototype.L;pv.prototype.un=pv.prototype.J;pv.prototype.unByKey=pv.prototype.M;U.prototype.getAttributions=U.prototype.xa;U.prototype.getLogo=U.prototype.wa;U.prototype.getProjection=U.prototype.ya;U.prototype.getState=U.prototype.V;U.prototype.refresh=U.prototype.va;U.prototype.setAttributions=U.prototype.ra;U.prototype.get=U.prototype.get;
U.prototype.getKeys=U.prototype.P;U.prototype.getProperties=U.prototype.N;U.prototype.set=U.prototype.set;U.prototype.setProperties=U.prototype.G;U.prototype.unset=U.prototype.S;U.prototype.changed=U.prototype.u;U.prototype.dispatchEvent=U.prototype.b;U.prototype.getRevision=U.prototype.K;U.prototype.on=U.prototype.I;U.prototype.once=U.prototype.L;U.prototype.un=U.prototype.J;U.prototype.unByKey=U.prototype.M;Y.prototype.addFeature=Y.prototype.ub;Y.prototype.addFeatures=Y.prototype.Hc;
Y.prototype.clear=Y.prototype.clear;Y.prototype.forEachFeature=Y.prototype.ug;Y.prototype.forEachFeatureInExtent=Y.prototype.xb;Y.prototype.forEachFeatureIntersectingExtent=Y.prototype.vg;Y.prototype.getFeaturesCollection=Y.prototype.Dg;Y.prototype.getFeatures=Y.prototype.me;Y.prototype.getFeaturesAtCoordinate=Y.prototype.Cg;Y.prototype.getFeaturesInExtent=Y.prototype.cf;Y.prototype.getClosestFeatureToCoordinate=Y.prototype.yg;Y.prototype.getExtent=Y.prototype.C;Y.prototype.getFeatureById=Y.prototype.Bg;
Y.prototype.getFormat=Y.prototype.zh;Y.prototype.getUrl=Y.prototype.Ah;Y.prototype.removeFeature=Y.prototype.pb;Y.prototype.getAttributions=Y.prototype.xa;Y.prototype.getLogo=Y.prototype.wa;Y.prototype.getProjection=Y.prototype.ya;Y.prototype.getState=Y.prototype.V;Y.prototype.refresh=Y.prototype.va;Y.prototype.setAttributions=Y.prototype.ra;Y.prototype.get=Y.prototype.get;Y.prototype.getKeys=Y.prototype.P;Y.prototype.getProperties=Y.prototype.N;Y.prototype.set=Y.prototype.set;
Y.prototype.setProperties=Y.prototype.G;Y.prototype.unset=Y.prototype.S;Y.prototype.changed=Y.prototype.u;Y.prototype.dispatchEvent=Y.prototype.b;Y.prototype.getRevision=Y.prototype.K;Y.prototype.on=Y.prototype.I;Y.prototype.once=Y.prototype.L;Y.prototype.un=Y.prototype.J;Y.prototype.unByKey=Y.prototype.M;tk.prototype.getAttributions=tk.prototype.xa;tk.prototype.getLogo=tk.prototype.wa;tk.prototype.getProjection=tk.prototype.ya;tk.prototype.getState=tk.prototype.V;tk.prototype.refresh=tk.prototype.va;
tk.prototype.setAttributions=tk.prototype.ra;tk.prototype.get=tk.prototype.get;tk.prototype.getKeys=tk.prototype.P;tk.prototype.getProperties=tk.prototype.N;tk.prototype.set=tk.prototype.set;tk.prototype.setProperties=tk.prototype.G;tk.prototype.unset=tk.prototype.S;tk.prototype.changed=tk.prototype.u;tk.prototype.dispatchEvent=tk.prototype.b;tk.prototype.getRevision=tk.prototype.K;tk.prototype.on=tk.prototype.I;tk.prototype.once=tk.prototype.L;tk.prototype.un=tk.prototype.J;
tk.prototype.unByKey=tk.prototype.M;vk.prototype.type=vk.prototype.type;vk.prototype.target=vk.prototype.target;vk.prototype.preventDefault=vk.prototype.preventDefault;vk.prototype.stopPropagation=vk.prototype.stopPropagation;vv.prototype.getAttributions=vv.prototype.xa;vv.prototype.getLogo=vv.prototype.wa;vv.prototype.getProjection=vv.prototype.ya;vv.prototype.getState=vv.prototype.V;vv.prototype.refresh=vv.prototype.va;vv.prototype.setAttributions=vv.prototype.ra;vv.prototype.get=vv.prototype.get;
vv.prototype.getKeys=vv.prototype.P;vv.prototype.getProperties=vv.prototype.N;vv.prototype.set=vv.prototype.set;vv.prototype.setProperties=vv.prototype.G;vv.prototype.unset=vv.prototype.S;vv.prototype.changed=vv.prototype.u;vv.prototype.dispatchEvent=vv.prototype.b;vv.prototype.getRevision=vv.prototype.K;vv.prototype.on=vv.prototype.I;vv.prototype.once=vv.prototype.L;vv.prototype.un=vv.prototype.J;vv.prototype.unByKey=vv.prototype.M;Ak.prototype.getAttributions=Ak.prototype.xa;
Ak.prototype.getLogo=Ak.prototype.wa;Ak.prototype.getProjection=Ak.prototype.ya;Ak.prototype.getState=Ak.prototype.V;Ak.prototype.refresh=Ak.prototype.va;Ak.prototype.setAttributions=Ak.prototype.ra;Ak.prototype.get=Ak.prototype.get;Ak.prototype.getKeys=Ak.prototype.P;Ak.prototype.getProperties=Ak.prototype.N;Ak.prototype.set=Ak.prototype.set;Ak.prototype.setProperties=Ak.prototype.G;Ak.prototype.unset=Ak.prototype.S;Ak.prototype.changed=Ak.prototype.u;Ak.prototype.dispatchEvent=Ak.prototype.b;
Ak.prototype.getRevision=Ak.prototype.K;Ak.prototype.on=Ak.prototype.I;Ak.prototype.once=Ak.prototype.L;Ak.prototype.un=Ak.prototype.J;Ak.prototype.unByKey=Ak.prototype.M;wv.prototype.getAttributions=wv.prototype.xa;wv.prototype.getLogo=wv.prototype.wa;wv.prototype.getProjection=wv.prototype.ya;wv.prototype.getState=wv.prototype.V;wv.prototype.refresh=wv.prototype.va;wv.prototype.setAttributions=wv.prototype.ra;wv.prototype.get=wv.prototype.get;wv.prototype.getKeys=wv.prototype.P;
wv.prototype.getProperties=wv.prototype.N;wv.prototype.set=wv.prototype.set;wv.prototype.setProperties=wv.prototype.G;wv.prototype.unset=wv.prototype.S;wv.prototype.changed=wv.prototype.u;wv.prototype.dispatchEvent=wv.prototype.b;wv.prototype.getRevision=wv.prototype.K;wv.prototype.on=wv.prototype.I;wv.prototype.once=wv.prototype.L;wv.prototype.un=wv.prototype.J;wv.prototype.unByKey=wv.prototype.M;xv.prototype.getAttributions=xv.prototype.xa;xv.prototype.getLogo=xv.prototype.wa;
xv.prototype.getProjection=xv.prototype.ya;xv.prototype.getState=xv.prototype.V;xv.prototype.refresh=xv.prototype.va;xv.prototype.setAttributions=xv.prototype.ra;xv.prototype.get=xv.prototype.get;xv.prototype.getKeys=xv.prototype.P;xv.prototype.getProperties=xv.prototype.N;xv.prototype.set=xv.prototype.set;xv.prototype.setProperties=xv.prototype.G;xv.prototype.unset=xv.prototype.S;xv.prototype.changed=xv.prototype.u;xv.prototype.dispatchEvent=xv.prototype.b;xv.prototype.getRevision=xv.prototype.K;
xv.prototype.on=xv.prototype.I;xv.prototype.once=xv.prototype.L;xv.prototype.un=xv.prototype.J;xv.prototype.unByKey=xv.prototype.M;Bk.prototype.getAttributions=Bk.prototype.xa;Bk.prototype.getLogo=Bk.prototype.wa;Bk.prototype.getProjection=Bk.prototype.ya;Bk.prototype.getState=Bk.prototype.V;Bk.prototype.refresh=Bk.prototype.va;Bk.prototype.setAttributions=Bk.prototype.ra;Bk.prototype.get=Bk.prototype.get;Bk.prototype.getKeys=Bk.prototype.P;Bk.prototype.getProperties=Bk.prototype.N;
Bk.prototype.set=Bk.prototype.set;Bk.prototype.setProperties=Bk.prototype.G;Bk.prototype.unset=Bk.prototype.S;Bk.prototype.changed=Bk.prototype.u;Bk.prototype.dispatchEvent=Bk.prototype.b;Bk.prototype.getRevision=Bk.prototype.K;Bk.prototype.on=Bk.prototype.I;Bk.prototype.once=Bk.prototype.L;Bk.prototype.un=Bk.prototype.J;Bk.prototype.unByKey=Bk.prototype.M;yv.prototype.getAttributions=yv.prototype.xa;yv.prototype.getLogo=yv.prototype.wa;yv.prototype.getProjection=yv.prototype.ya;
yv.prototype.getState=yv.prototype.V;yv.prototype.refresh=yv.prototype.va;yv.prototype.setAttributions=yv.prototype.ra;yv.prototype.get=yv.prototype.get;yv.prototype.getKeys=yv.prototype.P;yv.prototype.getProperties=yv.prototype.N;yv.prototype.set=yv.prototype.set;yv.prototype.setProperties=yv.prototype.G;yv.prototype.unset=yv.prototype.S;yv.prototype.changed=yv.prototype.u;yv.prototype.dispatchEvent=yv.prototype.b;yv.prototype.getRevision=yv.prototype.K;yv.prototype.on=yv.prototype.I;
yv.prototype.once=yv.prototype.L;yv.prototype.un=yv.prototype.J;yv.prototype.unByKey=yv.prototype.M;Cv.prototype.setRenderReprojectionEdges=Cv.prototype.Db;Cv.prototype.setTileGridForProjection=Cv.prototype.Eb;Cv.prototype.getTileLoadFunction=Cv.prototype.gb;Cv.prototype.getTileUrlFunction=Cv.prototype.hb;Cv.prototype.getUrls=Cv.prototype.ib;Cv.prototype.setTileLoadFunction=Cv.prototype.mb;Cv.prototype.setTileUrlFunction=Cv.prototype.Ta;Cv.prototype.setUrl=Cv.prototype.Za;Cv.prototype.setUrls=Cv.prototype.Ua;
Cv.prototype.getTileGrid=Cv.prototype.Pa;Cv.prototype.refresh=Cv.prototype.va;Cv.prototype.getAttributions=Cv.prototype.xa;Cv.prototype.getLogo=Cv.prototype.wa;Cv.prototype.getProjection=Cv.prototype.ya;Cv.prototype.getState=Cv.prototype.V;Cv.prototype.setAttributions=Cv.prototype.ra;Cv.prototype.get=Cv.prototype.get;Cv.prototype.getKeys=Cv.prototype.P;Cv.prototype.getProperties=Cv.prototype.N;Cv.prototype.set=Cv.prototype.set;Cv.prototype.setProperties=Cv.prototype.G;Cv.prototype.unset=Cv.prototype.S;
Cv.prototype.changed=Cv.prototype.u;Cv.prototype.dispatchEvent=Cv.prototype.b;Cv.prototype.getRevision=Cv.prototype.K;Cv.prototype.on=Cv.prototype.I;Cv.prototype.once=Cv.prototype.L;Cv.prototype.un=Cv.prototype.J;Cv.prototype.unByKey=Cv.prototype.M;Ev.prototype.getAttributions=Ev.prototype.xa;Ev.prototype.getLogo=Ev.prototype.wa;Ev.prototype.getProjection=Ev.prototype.ya;Ev.prototype.getState=Ev.prototype.V;Ev.prototype.refresh=Ev.prototype.va;Ev.prototype.setAttributions=Ev.prototype.ra;
Ev.prototype.get=Ev.prototype.get;Ev.prototype.getKeys=Ev.prototype.P;Ev.prototype.getProperties=Ev.prototype.N;Ev.prototype.set=Ev.prototype.set;Ev.prototype.setProperties=Ev.prototype.G;Ev.prototype.unset=Ev.prototype.S;Ev.prototype.changed=Ev.prototype.u;Ev.prototype.dispatchEvent=Ev.prototype.b;Ev.prototype.getRevision=Ev.prototype.K;Ev.prototype.on=Ev.prototype.I;Ev.prototype.once=Ev.prototype.L;Ev.prototype.un=Ev.prototype.J;Ev.prototype.unByKey=Ev.prototype.M;Jv.prototype.type=Jv.prototype.type;
Jv.prototype.target=Jv.prototype.target;Jv.prototype.preventDefault=Jv.prototype.preventDefault;Jv.prototype.stopPropagation=Jv.prototype.stopPropagation;Ov.prototype.setRenderReprojectionEdges=Ov.prototype.Db;Ov.prototype.setTileGridForProjection=Ov.prototype.Eb;Ov.prototype.getTileLoadFunction=Ov.prototype.gb;Ov.prototype.getTileUrlFunction=Ov.prototype.hb;Ov.prototype.getUrls=Ov.prototype.ib;Ov.prototype.setTileLoadFunction=Ov.prototype.mb;Ov.prototype.setTileUrlFunction=Ov.prototype.Ta;
Ov.prototype.setUrl=Ov.prototype.Za;Ov.prototype.setUrls=Ov.prototype.Ua;Ov.prototype.getTileGrid=Ov.prototype.Pa;Ov.prototype.refresh=Ov.prototype.va;Ov.prototype.getAttributions=Ov.prototype.xa;Ov.prototype.getLogo=Ov.prototype.wa;Ov.prototype.getProjection=Ov.prototype.ya;Ov.prototype.getState=Ov.prototype.V;Ov.prototype.setAttributions=Ov.prototype.ra;Ov.prototype.get=Ov.prototype.get;Ov.prototype.getKeys=Ov.prototype.P;Ov.prototype.getProperties=Ov.prototype.N;Ov.prototype.set=Ov.prototype.set;
Ov.prototype.setProperties=Ov.prototype.G;Ov.prototype.unset=Ov.prototype.S;Ov.prototype.changed=Ov.prototype.u;Ov.prototype.dispatchEvent=Ov.prototype.b;Ov.prototype.getRevision=Ov.prototype.K;Ov.prototype.on=Ov.prototype.I;Ov.prototype.once=Ov.prototype.L;Ov.prototype.un=Ov.prototype.J;Ov.prototype.unByKey=Ov.prototype.M;hv.prototype.type=hv.prototype.type;hv.prototype.target=hv.prototype.target;hv.prototype.preventDefault=hv.prototype.preventDefault;hv.prototype.stopPropagation=hv.prototype.stopPropagation;
Qv.prototype.setRenderReprojectionEdges=Qv.prototype.Db;Qv.prototype.setTileGridForProjection=Qv.prototype.Eb;Qv.prototype.getTileLoadFunction=Qv.prototype.gb;Qv.prototype.getTileUrlFunction=Qv.prototype.hb;Qv.prototype.getUrls=Qv.prototype.ib;Qv.prototype.setTileLoadFunction=Qv.prototype.mb;Qv.prototype.setTileUrlFunction=Qv.prototype.Ta;Qv.prototype.setUrl=Qv.prototype.Za;Qv.prototype.setUrls=Qv.prototype.Ua;Qv.prototype.getTileGrid=Qv.prototype.Pa;Qv.prototype.refresh=Qv.prototype.va;
Qv.prototype.getAttributions=Qv.prototype.xa;Qv.prototype.getLogo=Qv.prototype.wa;Qv.prototype.getProjection=Qv.prototype.ya;Qv.prototype.getState=Qv.prototype.V;Qv.prototype.setAttributions=Qv.prototype.ra;Qv.prototype.get=Qv.prototype.get;Qv.prototype.getKeys=Qv.prototype.P;Qv.prototype.getProperties=Qv.prototype.N;Qv.prototype.set=Qv.prototype.set;Qv.prototype.setProperties=Qv.prototype.G;Qv.prototype.unset=Qv.prototype.S;Qv.prototype.changed=Qv.prototype.u;Qv.prototype.dispatchEvent=Qv.prototype.b;
Qv.prototype.getRevision=Qv.prototype.K;Qv.prototype.on=Qv.prototype.I;Qv.prototype.once=Qv.prototype.L;Qv.prototype.un=Qv.prototype.J;Qv.prototype.unByKey=Qv.prototype.M;Sv.prototype.getTileGrid=Sv.prototype.Pa;Sv.prototype.refresh=Sv.prototype.va;Sv.prototype.getAttributions=Sv.prototype.xa;Sv.prototype.getLogo=Sv.prototype.wa;Sv.prototype.getProjection=Sv.prototype.ya;Sv.prototype.getState=Sv.prototype.V;Sv.prototype.setAttributions=Sv.prototype.ra;Sv.prototype.get=Sv.prototype.get;
Sv.prototype.getKeys=Sv.prototype.P;Sv.prototype.getProperties=Sv.prototype.N;Sv.prototype.set=Sv.prototype.set;Sv.prototype.setProperties=Sv.prototype.G;Sv.prototype.unset=Sv.prototype.S;Sv.prototype.changed=Sv.prototype.u;Sv.prototype.dispatchEvent=Sv.prototype.b;Sv.prototype.getRevision=Sv.prototype.K;Sv.prototype.on=Sv.prototype.I;Sv.prototype.once=Sv.prototype.L;Sv.prototype.un=Sv.prototype.J;Sv.prototype.unByKey=Sv.prototype.M;Tv.prototype.setRenderReprojectionEdges=Tv.prototype.Db;
Tv.prototype.setTileGridForProjection=Tv.prototype.Eb;Tv.prototype.getTileLoadFunction=Tv.prototype.gb;Tv.prototype.getTileUrlFunction=Tv.prototype.hb;Tv.prototype.getUrls=Tv.prototype.ib;Tv.prototype.setTileLoadFunction=Tv.prototype.mb;Tv.prototype.setTileUrlFunction=Tv.prototype.Ta;Tv.prototype.setUrl=Tv.prototype.Za;Tv.prototype.setUrls=Tv.prototype.Ua;Tv.prototype.getTileGrid=Tv.prototype.Pa;Tv.prototype.refresh=Tv.prototype.va;Tv.prototype.getAttributions=Tv.prototype.xa;
Tv.prototype.getLogo=Tv.prototype.wa;Tv.prototype.getProjection=Tv.prototype.ya;Tv.prototype.getState=Tv.prototype.V;Tv.prototype.setAttributions=Tv.prototype.ra;Tv.prototype.get=Tv.prototype.get;Tv.prototype.getKeys=Tv.prototype.P;Tv.prototype.getProperties=Tv.prototype.N;Tv.prototype.set=Tv.prototype.set;Tv.prototype.setProperties=Tv.prototype.G;Tv.prototype.unset=Tv.prototype.S;Tv.prototype.changed=Tv.prototype.u;Tv.prototype.dispatchEvent=Tv.prototype.b;Tv.prototype.getRevision=Tv.prototype.K;
Tv.prototype.on=Tv.prototype.I;Tv.prototype.once=Tv.prototype.L;Tv.prototype.un=Tv.prototype.J;Tv.prototype.unByKey=Tv.prototype.M;Uv.prototype.getTileGrid=Uv.prototype.Pa;Uv.prototype.refresh=Uv.prototype.va;Uv.prototype.getAttributions=Uv.prototype.xa;Uv.prototype.getLogo=Uv.prototype.wa;Uv.prototype.getProjection=Uv.prototype.ya;Uv.prototype.getState=Uv.prototype.V;Uv.prototype.setAttributions=Uv.prototype.ra;Uv.prototype.get=Uv.prototype.get;Uv.prototype.getKeys=Uv.prototype.P;
Uv.prototype.getProperties=Uv.prototype.N;Uv.prototype.set=Uv.prototype.set;Uv.prototype.setProperties=Uv.prototype.G;Uv.prototype.unset=Uv.prototype.S;Uv.prototype.changed=Uv.prototype.u;Uv.prototype.dispatchEvent=Uv.prototype.b;Uv.prototype.getRevision=Uv.prototype.K;Uv.prototype.on=Uv.prototype.I;Uv.prototype.once=Uv.prototype.L;Uv.prototype.un=Uv.prototype.J;Uv.prototype.unByKey=Uv.prototype.M;Yv.prototype.setRenderReprojectionEdges=Yv.prototype.Db;Yv.prototype.setTileGridForProjection=Yv.prototype.Eb;
Yv.prototype.getTileLoadFunction=Yv.prototype.gb;Yv.prototype.getTileUrlFunction=Yv.prototype.hb;Yv.prototype.getUrls=Yv.prototype.ib;Yv.prototype.setTileLoadFunction=Yv.prototype.mb;Yv.prototype.setTileUrlFunction=Yv.prototype.Ta;Yv.prototype.setUrl=Yv.prototype.Za;Yv.prototype.setUrls=Yv.prototype.Ua;Yv.prototype.getTileGrid=Yv.prototype.Pa;Yv.prototype.refresh=Yv.prototype.va;Yv.prototype.getAttributions=Yv.prototype.xa;Yv.prototype.getLogo=Yv.prototype.wa;Yv.prototype.getProjection=Yv.prototype.ya;
Yv.prototype.getState=Yv.prototype.V;Yv.prototype.setAttributions=Yv.prototype.ra;Yv.prototype.get=Yv.prototype.get;Yv.prototype.getKeys=Yv.prototype.P;Yv.prototype.getProperties=Yv.prototype.N;Yv.prototype.set=Yv.prototype.set;Yv.prototype.setProperties=Yv.prototype.G;Yv.prototype.unset=Yv.prototype.S;Yv.prototype.changed=Yv.prototype.u;Yv.prototype.dispatchEvent=Yv.prototype.b;Yv.prototype.getRevision=Yv.prototype.K;Yv.prototype.on=Yv.prototype.I;Yv.prototype.once=Yv.prototype.L;
Yv.prototype.un=Yv.prototype.J;Yv.prototype.unByKey=Yv.prototype.M;Zt.prototype.type=Zt.prototype.type;Zt.prototype.target=Zt.prototype.target;Zt.prototype.preventDefault=Zt.prototype.preventDefault;Zt.prototype.stopPropagation=Zt.prototype.stopPropagation;dw.prototype.getTileLoadFunction=dw.prototype.gb;dw.prototype.getTileUrlFunction=dw.prototype.hb;dw.prototype.getUrls=dw.prototype.ib;dw.prototype.setTileLoadFunction=dw.prototype.mb;dw.prototype.setTileUrlFunction=dw.prototype.Ta;
dw.prototype.setUrl=dw.prototype.Za;dw.prototype.setUrls=dw.prototype.Ua;dw.prototype.getTileGrid=dw.prototype.Pa;dw.prototype.refresh=dw.prototype.va;dw.prototype.getAttributions=dw.prototype.xa;dw.prototype.getLogo=dw.prototype.wa;dw.prototype.getProjection=dw.prototype.ya;dw.prototype.getState=dw.prototype.V;dw.prototype.setAttributions=dw.prototype.ra;dw.prototype.get=dw.prototype.get;dw.prototype.getKeys=dw.prototype.P;dw.prototype.getProperties=dw.prototype.N;dw.prototype.set=dw.prototype.set;
dw.prototype.setProperties=dw.prototype.G;dw.prototype.unset=dw.prototype.S;dw.prototype.changed=dw.prototype.u;dw.prototype.dispatchEvent=dw.prototype.b;dw.prototype.getRevision=dw.prototype.K;dw.prototype.on=dw.prototype.I;dw.prototype.once=dw.prototype.L;dw.prototype.un=dw.prototype.J;dw.prototype.unByKey=dw.prototype.M;Z.prototype.setRenderReprojectionEdges=Z.prototype.Db;Z.prototype.setTileGridForProjection=Z.prototype.Eb;Z.prototype.getTileLoadFunction=Z.prototype.gb;
Z.prototype.getTileUrlFunction=Z.prototype.hb;Z.prototype.getUrls=Z.prototype.ib;Z.prototype.setTileLoadFunction=Z.prototype.mb;Z.prototype.setTileUrlFunction=Z.prototype.Ta;Z.prototype.setUrl=Z.prototype.Za;Z.prototype.setUrls=Z.prototype.Ua;Z.prototype.getTileGrid=Z.prototype.Pa;Z.prototype.refresh=Z.prototype.va;Z.prototype.getAttributions=Z.prototype.xa;Z.prototype.getLogo=Z.prototype.wa;Z.prototype.getProjection=Z.prototype.ya;Z.prototype.getState=Z.prototype.V;Z.prototype.setAttributions=Z.prototype.ra;
Z.prototype.get=Z.prototype.get;Z.prototype.getKeys=Z.prototype.P;Z.prototype.getProperties=Z.prototype.N;Z.prototype.set=Z.prototype.set;Z.prototype.setProperties=Z.prototype.G;Z.prototype.unset=Z.prototype.S;Z.prototype.changed=Z.prototype.u;Z.prototype.dispatchEvent=Z.prototype.b;Z.prototype.getRevision=Z.prototype.K;Z.prototype.on=Z.prototype.I;Z.prototype.once=Z.prototype.L;Z.prototype.un=Z.prototype.J;Z.prototype.unByKey=Z.prototype.M;iw.prototype.setRenderReprojectionEdges=iw.prototype.Db;
iw.prototype.setTileGridForProjection=iw.prototype.Eb;iw.prototype.getTileLoadFunction=iw.prototype.gb;iw.prototype.getTileUrlFunction=iw.prototype.hb;iw.prototype.getUrls=iw.prototype.ib;iw.prototype.setTileLoadFunction=iw.prototype.mb;iw.prototype.setTileUrlFunction=iw.prototype.Ta;iw.prototype.setUrl=iw.prototype.Za;iw.prototype.setUrls=iw.prototype.Ua;iw.prototype.getTileGrid=iw.prototype.Pa;iw.prototype.refresh=iw.prototype.va;iw.prototype.getAttributions=iw.prototype.xa;
iw.prototype.getLogo=iw.prototype.wa;iw.prototype.getProjection=iw.prototype.ya;iw.prototype.getState=iw.prototype.V;iw.prototype.setAttributions=iw.prototype.ra;iw.prototype.get=iw.prototype.get;iw.prototype.getKeys=iw.prototype.P;iw.prototype.getProperties=iw.prototype.N;iw.prototype.set=iw.prototype.set;iw.prototype.setProperties=iw.prototype.G;iw.prototype.unset=iw.prototype.S;iw.prototype.changed=iw.prototype.u;iw.prototype.dispatchEvent=iw.prototype.b;iw.prototype.getRevision=iw.prototype.K;
iw.prototype.on=iw.prototype.I;iw.prototype.once=iw.prototype.L;iw.prototype.un=iw.prototype.J;iw.prototype.unByKey=iw.prototype.M;Xu.prototype.getTileCoord=Xu.prototype.i;Xu.prototype.load=Xu.prototype.load;tj.prototype.changed=tj.prototype.u;tj.prototype.dispatchEvent=tj.prototype.b;tj.prototype.getRevision=tj.prototype.K;tj.prototype.on=tj.prototype.I;tj.prototype.once=tj.prototype.L;tj.prototype.un=tj.prototype.J;tj.prototype.unByKey=tj.prototype.M;Hl.prototype.changed=Hl.prototype.u;
Hl.prototype.dispatchEvent=Hl.prototype.b;Hl.prototype.getRevision=Hl.prototype.K;Hl.prototype.on=Hl.prototype.I;Hl.prototype.once=Hl.prototype.L;Hl.prototype.un=Hl.prototype.J;Hl.prototype.unByKey=Hl.prototype.M;Kl.prototype.changed=Kl.prototype.u;Kl.prototype.dispatchEvent=Kl.prototype.b;Kl.prototype.getRevision=Kl.prototype.K;Kl.prototype.on=Kl.prototype.I;Kl.prototype.once=Kl.prototype.L;Kl.prototype.un=Kl.prototype.J;Kl.prototype.unByKey=Kl.prototype.M;Sl.prototype.changed=Sl.prototype.u;
Sl.prototype.dispatchEvent=Sl.prototype.b;Sl.prototype.getRevision=Sl.prototype.K;Sl.prototype.on=Sl.prototype.I;Sl.prototype.once=Sl.prototype.L;Sl.prototype.un=Sl.prototype.J;Sl.prototype.unByKey=Sl.prototype.M;Ul.prototype.changed=Ul.prototype.u;Ul.prototype.dispatchEvent=Ul.prototype.b;Ul.prototype.getRevision=Ul.prototype.K;Ul.prototype.on=Ul.prototype.I;Ul.prototype.once=Ul.prototype.L;Ul.prototype.un=Ul.prototype.J;Ul.prototype.unByKey=Ul.prototype.M;Nk.prototype.changed=Nk.prototype.u;
Nk.prototype.dispatchEvent=Nk.prototype.b;Nk.prototype.getRevision=Nk.prototype.K;Nk.prototype.on=Nk.prototype.I;Nk.prototype.once=Nk.prototype.L;Nk.prototype.un=Nk.prototype.J;Nk.prototype.unByKey=Nk.prototype.M;Ok.prototype.changed=Ok.prototype.u;Ok.prototype.dispatchEvent=Ok.prototype.b;Ok.prototype.getRevision=Ok.prototype.K;Ok.prototype.on=Ok.prototype.I;Ok.prototype.once=Ok.prototype.L;Ok.prototype.un=Ok.prototype.J;Ok.prototype.unByKey=Ok.prototype.M;Pk.prototype.changed=Pk.prototype.u;
Pk.prototype.dispatchEvent=Pk.prototype.b;Pk.prototype.getRevision=Pk.prototype.K;Pk.prototype.on=Pk.prototype.I;Pk.prototype.once=Pk.prototype.L;Pk.prototype.un=Pk.prototype.J;Pk.prototype.unByKey=Pk.prototype.M;Rk.prototype.changed=Rk.prototype.u;Rk.prototype.dispatchEvent=Rk.prototype.b;Rk.prototype.getRevision=Rk.prototype.K;Rk.prototype.on=Rk.prototype.I;Rk.prototype.once=Rk.prototype.L;Rk.prototype.un=Rk.prototype.J;Rk.prototype.unByKey=Rk.prototype.M;Dj.prototype.changed=Dj.prototype.u;
Dj.prototype.dispatchEvent=Dj.prototype.b;Dj.prototype.getRevision=Dj.prototype.K;Dj.prototype.on=Dj.prototype.I;Dj.prototype.once=Dj.prototype.L;Dj.prototype.un=Dj.prototype.J;Dj.prototype.unByKey=Dj.prototype.M;Dk.prototype.changed=Dk.prototype.u;Dk.prototype.dispatchEvent=Dk.prototype.b;Dk.prototype.getRevision=Dk.prototype.K;Dk.prototype.on=Dk.prototype.I;Dk.prototype.once=Dk.prototype.L;Dk.prototype.un=Dk.prototype.J;Dk.prototype.unByKey=Dk.prototype.M;Ek.prototype.changed=Ek.prototype.u;
Ek.prototype.dispatchEvent=Ek.prototype.b;Ek.prototype.getRevision=Ek.prototype.K;Ek.prototype.on=Ek.prototype.I;Ek.prototype.once=Ek.prototype.L;Ek.prototype.un=Ek.prototype.J;Ek.prototype.unByKey=Ek.prototype.M;Gk.prototype.changed=Gk.prototype.u;Gk.prototype.dispatchEvent=Gk.prototype.b;Gk.prototype.getRevision=Gk.prototype.K;Gk.prototype.on=Gk.prototype.I;Gk.prototype.once=Gk.prototype.L;Gk.prototype.un=Gk.prototype.J;Gk.prototype.unByKey=Gk.prototype.M;Jk.prototype.changed=Jk.prototype.u;
Jk.prototype.dispatchEvent=Jk.prototype.b;Jk.prototype.getRevision=Jk.prototype.K;Jk.prototype.on=Jk.prototype.I;Jk.prototype.once=Jk.prototype.L;Jk.prototype.un=Jk.prototype.J;Jk.prototype.unByKey=Jk.prototype.M;Sg.prototype.type=Sg.prototype.type;Sg.prototype.target=Sg.prototype.target;Sg.prototype.preventDefault=Sg.prototype.preventDefault;Sg.prototype.stopPropagation=Sg.prototype.stopPropagation;jg.prototype.type=jg.prototype.type;jg.prototype.target=jg.prototype.target;
jg.prototype.preventDefault=jg.prototype.preventDefault;jg.prototype.stopPropagation=jg.prototype.stopPropagation;Qg.prototype.get=Qg.prototype.get;Qg.prototype.getKeys=Qg.prototype.P;Qg.prototype.getProperties=Qg.prototype.N;Qg.prototype.set=Qg.prototype.set;Qg.prototype.setProperties=Qg.prototype.G;Qg.prototype.unset=Qg.prototype.S;Qg.prototype.changed=Qg.prototype.u;Qg.prototype.dispatchEvent=Qg.prototype.b;Qg.prototype.getRevision=Qg.prototype.K;Qg.prototype.on=Qg.prototype.I;
Qg.prototype.once=Qg.prototype.L;Qg.prototype.un=Qg.prototype.J;Qg.prototype.unByKey=Qg.prototype.M;zi.prototype.getExtent=zi.prototype.C;zi.prototype.getMaxResolution=zi.prototype.Rb;zi.prototype.getMinResolution=zi.prototype.Sb;zi.prototype.getOpacity=zi.prototype.Tb;zi.prototype.getVisible=zi.prototype.Bb;zi.prototype.getZIndex=zi.prototype.Ub;zi.prototype.setExtent=zi.prototype.ic;zi.prototype.setMaxResolution=zi.prototype.oc;zi.prototype.setMinResolution=zi.prototype.pc;
zi.prototype.setOpacity=zi.prototype.jc;zi.prototype.setVisible=zi.prototype.kc;zi.prototype.setZIndex=zi.prototype.lc;zi.prototype.get=zi.prototype.get;zi.prototype.getKeys=zi.prototype.P;zi.prototype.getProperties=zi.prototype.N;zi.prototype.set=zi.prototype.set;zi.prototype.setProperties=zi.prototype.G;zi.prototype.unset=zi.prototype.S;zi.prototype.changed=zi.prototype.u;zi.prototype.dispatchEvent=zi.prototype.b;zi.prototype.getRevision=zi.prototype.K;zi.prototype.on=zi.prototype.I;
zi.prototype.once=zi.prototype.L;zi.prototype.un=zi.prototype.J;zi.prototype.unByKey=zi.prototype.M;Wg.prototype.getExtent=Wg.prototype.C;Wg.prototype.getMaxResolution=Wg.prototype.Rb;Wg.prototype.getMinResolution=Wg.prototype.Sb;Wg.prototype.getOpacity=Wg.prototype.Tb;Wg.prototype.getVisible=Wg.prototype.Bb;Wg.prototype.getZIndex=Wg.prototype.Ub;Wg.prototype.setExtent=Wg.prototype.ic;Wg.prototype.setMaxResolution=Wg.prototype.oc;Wg.prototype.setMinResolution=Wg.prototype.pc;
Wg.prototype.setOpacity=Wg.prototype.jc;Wg.prototype.setVisible=Wg.prototype.kc;Wg.prototype.setZIndex=Wg.prototype.lc;Wg.prototype.get=Wg.prototype.get;Wg.prototype.getKeys=Wg.prototype.P;Wg.prototype.getProperties=Wg.prototype.N;Wg.prototype.set=Wg.prototype.set;Wg.prototype.setProperties=Wg.prototype.G;Wg.prototype.unset=Wg.prototype.S;Wg.prototype.changed=Wg.prototype.u;Wg.prototype.dispatchEvent=Wg.prototype.b;Wg.prototype.getRevision=Wg.prototype.K;Wg.prototype.on=Wg.prototype.I;
Wg.prototype.once=Wg.prototype.L;Wg.prototype.un=Wg.prototype.J;Wg.prototype.unByKey=Wg.prototype.M;F.prototype.setMap=F.prototype.setMap;F.prototype.setSource=F.prototype.Dc;F.prototype.getExtent=F.prototype.C;F.prototype.getMaxResolution=F.prototype.Rb;F.prototype.getMinResolution=F.prototype.Sb;F.prototype.getOpacity=F.prototype.Tb;F.prototype.getVisible=F.prototype.Bb;F.prototype.getZIndex=F.prototype.Ub;F.prototype.setExtent=F.prototype.ic;F.prototype.setMaxResolution=F.prototype.oc;
F.prototype.setMinResolution=F.prototype.pc;F.prototype.setOpacity=F.prototype.jc;F.prototype.setVisible=F.prototype.kc;F.prototype.setZIndex=F.prototype.lc;F.prototype.get=F.prototype.get;F.prototype.getKeys=F.prototype.P;F.prototype.getProperties=F.prototype.N;F.prototype.set=F.prototype.set;F.prototype.setProperties=F.prototype.G;F.prototype.unset=F.prototype.S;F.prototype.changed=F.prototype.u;F.prototype.dispatchEvent=F.prototype.b;F.prototype.getRevision=F.prototype.K;F.prototype.on=F.prototype.I;
F.prototype.once=F.prototype.L;F.prototype.un=F.prototype.J;F.prototype.unByKey=F.prototype.M;V.prototype.getSource=V.prototype.ga;V.prototype.getStyle=V.prototype.H;V.prototype.getStyleFunction=V.prototype.D;V.prototype.setStyle=V.prototype.l;V.prototype.setMap=V.prototype.setMap;V.prototype.setSource=V.prototype.Dc;V.prototype.getExtent=V.prototype.C;V.prototype.getMaxResolution=V.prototype.Rb;V.prototype.getMinResolution=V.prototype.Sb;V.prototype.getOpacity=V.prototype.Tb;
V.prototype.getVisible=V.prototype.Bb;V.prototype.getZIndex=V.prototype.Ub;V.prototype.setExtent=V.prototype.ic;V.prototype.setMaxResolution=V.prototype.oc;V.prototype.setMinResolution=V.prototype.pc;V.prototype.setOpacity=V.prototype.jc;V.prototype.setVisible=V.prototype.kc;V.prototype.setZIndex=V.prototype.lc;V.prototype.get=V.prototype.get;V.prototype.getKeys=V.prototype.P;V.prototype.getProperties=V.prototype.N;V.prototype.set=V.prototype.set;V.prototype.setProperties=V.prototype.G;
V.prototype.unset=V.prototype.S;V.prototype.changed=V.prototype.u;V.prototype.dispatchEvent=V.prototype.b;V.prototype.getRevision=V.prototype.K;V.prototype.on=V.prototype.I;V.prototype.once=V.prototype.L;V.prototype.un=V.prototype.J;V.prototype.unByKey=V.prototype.M;Ji.prototype.setMap=Ji.prototype.setMap;Ji.prototype.setSource=Ji.prototype.Dc;Ji.prototype.getExtent=Ji.prototype.C;Ji.prototype.getMaxResolution=Ji.prototype.Rb;Ji.prototype.getMinResolution=Ji.prototype.Sb;Ji.prototype.getOpacity=Ji.prototype.Tb;
Ji.prototype.getVisible=Ji.prototype.Bb;Ji.prototype.getZIndex=Ji.prototype.Ub;Ji.prototype.setExtent=Ji.prototype.ic;Ji.prototype.setMaxResolution=Ji.prototype.oc;Ji.prototype.setMinResolution=Ji.prototype.pc;Ji.prototype.setOpacity=Ji.prototype.jc;Ji.prototype.setVisible=Ji.prototype.kc;Ji.prototype.setZIndex=Ji.prototype.lc;Ji.prototype.get=Ji.prototype.get;Ji.prototype.getKeys=Ji.prototype.P;Ji.prototype.getProperties=Ji.prototype.N;Ji.prototype.set=Ji.prototype.set;
Ji.prototype.setProperties=Ji.prototype.G;Ji.prototype.unset=Ji.prototype.S;Ji.prototype.changed=Ji.prototype.u;Ji.prototype.dispatchEvent=Ji.prototype.b;Ji.prototype.getRevision=Ji.prototype.K;Ji.prototype.on=Ji.prototype.I;Ji.prototype.once=Ji.prototype.L;Ji.prototype.un=Ji.prototype.J;Ji.prototype.unByKey=Ji.prototype.M;Ki.prototype.setMap=Ki.prototype.setMap;Ki.prototype.setSource=Ki.prototype.Dc;Ki.prototype.getExtent=Ki.prototype.C;Ki.prototype.getMaxResolution=Ki.prototype.Rb;
Ki.prototype.getMinResolution=Ki.prototype.Sb;Ki.prototype.getOpacity=Ki.prototype.Tb;Ki.prototype.getVisible=Ki.prototype.Bb;Ki.prototype.getZIndex=Ki.prototype.Ub;Ki.prototype.setExtent=Ki.prototype.ic;Ki.prototype.setMaxResolution=Ki.prototype.oc;Ki.prototype.setMinResolution=Ki.prototype.pc;Ki.prototype.setOpacity=Ki.prototype.jc;Ki.prototype.setVisible=Ki.prototype.kc;Ki.prototype.setZIndex=Ki.prototype.lc;Ki.prototype.get=Ki.prototype.get;Ki.prototype.getKeys=Ki.prototype.P;
Ki.prototype.getProperties=Ki.prototype.N;Ki.prototype.set=Ki.prototype.set;Ki.prototype.setProperties=Ki.prototype.G;Ki.prototype.unset=Ki.prototype.S;Ki.prototype.changed=Ki.prototype.u;Ki.prototype.dispatchEvent=Ki.prototype.b;Ki.prototype.getRevision=Ki.prototype.K;Ki.prototype.on=Ki.prototype.I;Ki.prototype.once=Ki.prototype.L;Ki.prototype.un=Ki.prototype.J;Ki.prototype.unByKey=Ki.prototype.M;H.prototype.getSource=H.prototype.ga;H.prototype.getStyle=H.prototype.H;
H.prototype.getStyleFunction=H.prototype.D;H.prototype.setStyle=H.prototype.l;H.prototype.setMap=H.prototype.setMap;H.prototype.setSource=H.prototype.Dc;H.prototype.getExtent=H.prototype.C;H.prototype.getMaxResolution=H.prototype.Rb;H.prototype.getMinResolution=H.prototype.Sb;H.prototype.getOpacity=H.prototype.Tb;H.prototype.getVisible=H.prototype.Bb;H.prototype.getZIndex=H.prototype.Ub;H.prototype.setExtent=H.prototype.ic;H.prototype.setMaxResolution=H.prototype.oc;H.prototype.setMinResolution=H.prototype.pc;
H.prototype.setOpacity=H.prototype.jc;H.prototype.setVisible=H.prototype.kc;H.prototype.setZIndex=H.prototype.lc;H.prototype.get=H.prototype.get;H.prototype.getKeys=H.prototype.P;H.prototype.getProperties=H.prototype.N;H.prototype.set=H.prototype.set;H.prototype.setProperties=H.prototype.G;H.prototype.unset=H.prototype.S;H.prototype.changed=H.prototype.u;H.prototype.dispatchEvent=H.prototype.b;H.prototype.getRevision=H.prototype.K;H.prototype.on=H.prototype.I;H.prototype.once=H.prototype.L;
H.prototype.un=H.prototype.J;H.prototype.unByKey=H.prototype.M;Bh.prototype.get=Bh.prototype.get;Bh.prototype.getKeys=Bh.prototype.P;Bh.prototype.getProperties=Bh.prototype.N;Bh.prototype.set=Bh.prototype.set;Bh.prototype.setProperties=Bh.prototype.G;Bh.prototype.unset=Bh.prototype.S;Bh.prototype.changed=Bh.prototype.u;Bh.prototype.dispatchEvent=Bh.prototype.b;Bh.prototype.getRevision=Bh.prototype.K;Bh.prototype.on=Bh.prototype.I;Bh.prototype.once=Bh.prototype.L;Bh.prototype.un=Bh.prototype.J;
Bh.prototype.unByKey=Bh.prototype.M;Fh.prototype.getActive=Fh.prototype.f;Fh.prototype.getMap=Fh.prototype.l;Fh.prototype.setActive=Fh.prototype.i;Fh.prototype.get=Fh.prototype.get;Fh.prototype.getKeys=Fh.prototype.P;Fh.prototype.getProperties=Fh.prototype.N;Fh.prototype.set=Fh.prototype.set;Fh.prototype.setProperties=Fh.prototype.G;Fh.prototype.unset=Fh.prototype.S;Fh.prototype.changed=Fh.prototype.u;Fh.prototype.dispatchEvent=Fh.prototype.b;Fh.prototype.getRevision=Fh.prototype.K;
Fh.prototype.on=Fh.prototype.I;Fh.prototype.once=Fh.prototype.L;Fh.prototype.un=Fh.prototype.J;Fh.prototype.unByKey=Fh.prototype.M;Ft.prototype.getActive=Ft.prototype.f;Ft.prototype.getMap=Ft.prototype.l;Ft.prototype.setActive=Ft.prototype.i;Ft.prototype.get=Ft.prototype.get;Ft.prototype.getKeys=Ft.prototype.P;Ft.prototype.getProperties=Ft.prototype.N;Ft.prototype.set=Ft.prototype.set;Ft.prototype.setProperties=Ft.prototype.G;Ft.prototype.unset=Ft.prototype.S;Ft.prototype.changed=Ft.prototype.u;
Ft.prototype.dispatchEvent=Ft.prototype.b;Ft.prototype.getRevision=Ft.prototype.K;Ft.prototype.on=Ft.prototype.I;Ft.prototype.once=Ft.prototype.L;Ft.prototype.un=Ft.prototype.J;Ft.prototype.unByKey=Ft.prototype.M;It.prototype.type=It.prototype.type;It.prototype.target=It.prototype.target;It.prototype.preventDefault=It.prototype.preventDefault;It.prototype.stopPropagation=It.prototype.stopPropagation;di.prototype.type=di.prototype.type;di.prototype.target=di.prototype.target;
di.prototype.preventDefault=di.prototype.preventDefault;di.prototype.stopPropagation=di.prototype.stopPropagation;Qh.prototype.getActive=Qh.prototype.f;Qh.prototype.getMap=Qh.prototype.l;Qh.prototype.setActive=Qh.prototype.i;Qh.prototype.get=Qh.prototype.get;Qh.prototype.getKeys=Qh.prototype.P;Qh.prototype.getProperties=Qh.prototype.N;Qh.prototype.set=Qh.prototype.set;Qh.prototype.setProperties=Qh.prototype.G;Qh.prototype.unset=Qh.prototype.S;Qh.prototype.changed=Qh.prototype.u;
Qh.prototype.dispatchEvent=Qh.prototype.b;Qh.prototype.getRevision=Qh.prototype.K;Qh.prototype.on=Qh.prototype.I;Qh.prototype.once=Qh.prototype.L;Qh.prototype.un=Qh.prototype.J;Qh.prototype.unByKey=Qh.prototype.M;ei.prototype.getActive=ei.prototype.f;ei.prototype.getMap=ei.prototype.l;ei.prototype.setActive=ei.prototype.i;ei.prototype.get=ei.prototype.get;ei.prototype.getKeys=ei.prototype.P;ei.prototype.getProperties=ei.prototype.N;ei.prototype.set=ei.prototype.set;ei.prototype.setProperties=ei.prototype.G;
ei.prototype.unset=ei.prototype.S;ei.prototype.changed=ei.prototype.u;ei.prototype.dispatchEvent=ei.prototype.b;ei.prototype.getRevision=ei.prototype.K;ei.prototype.on=ei.prototype.I;ei.prototype.once=ei.prototype.L;ei.prototype.un=ei.prototype.J;ei.prototype.unByKey=ei.prototype.M;Th.prototype.getActive=Th.prototype.f;Th.prototype.getMap=Th.prototype.l;Th.prototype.setActive=Th.prototype.i;Th.prototype.get=Th.prototype.get;Th.prototype.getKeys=Th.prototype.P;Th.prototype.getProperties=Th.prototype.N;
Th.prototype.set=Th.prototype.set;Th.prototype.setProperties=Th.prototype.G;Th.prototype.unset=Th.prototype.S;Th.prototype.changed=Th.prototype.u;Th.prototype.dispatchEvent=Th.prototype.b;Th.prototype.getRevision=Th.prototype.K;Th.prototype.on=Th.prototype.I;Th.prototype.once=Th.prototype.L;Th.prototype.un=Th.prototype.J;Th.prototype.unByKey=Th.prototype.M;Xh.prototype.getActive=Xh.prototype.f;Xh.prototype.getMap=Xh.prototype.l;Xh.prototype.setActive=Xh.prototype.i;Xh.prototype.get=Xh.prototype.get;
Xh.prototype.getKeys=Xh.prototype.P;Xh.prototype.getProperties=Xh.prototype.N;Xh.prototype.set=Xh.prototype.set;Xh.prototype.setProperties=Xh.prototype.G;Xh.prototype.unset=Xh.prototype.S;Xh.prototype.changed=Xh.prototype.u;Xh.prototype.dispatchEvent=Xh.prototype.b;Xh.prototype.getRevision=Xh.prototype.K;Xh.prototype.on=Xh.prototype.I;Xh.prototype.once=Xh.prototype.L;Xh.prototype.un=Xh.prototype.J;Xh.prototype.unByKey=Xh.prototype.M;Kt.prototype.getActive=Kt.prototype.f;Kt.prototype.getMap=Kt.prototype.l;
Kt.prototype.setActive=Kt.prototype.i;Kt.prototype.get=Kt.prototype.get;Kt.prototype.getKeys=Kt.prototype.P;Kt.prototype.getProperties=Kt.prototype.N;Kt.prototype.set=Kt.prototype.set;Kt.prototype.setProperties=Kt.prototype.G;Kt.prototype.unset=Kt.prototype.S;Kt.prototype.changed=Kt.prototype.u;Kt.prototype.dispatchEvent=Kt.prototype.b;Kt.prototype.getRevision=Kt.prototype.K;Kt.prototype.on=Kt.prototype.I;Kt.prototype.once=Kt.prototype.L;Kt.prototype.un=Kt.prototype.J;Kt.prototype.unByKey=Kt.prototype.M;
ji.prototype.getGeometry=ji.prototype.Y;ji.prototype.getActive=ji.prototype.f;ji.prototype.getMap=ji.prototype.l;ji.prototype.setActive=ji.prototype.i;ji.prototype.get=ji.prototype.get;ji.prototype.getKeys=ji.prototype.P;ji.prototype.getProperties=ji.prototype.N;ji.prototype.set=ji.prototype.set;ji.prototype.setProperties=ji.prototype.G;ji.prototype.unset=ji.prototype.S;ji.prototype.changed=ji.prototype.u;ji.prototype.dispatchEvent=ji.prototype.b;ji.prototype.getRevision=ji.prototype.K;
ji.prototype.on=ji.prototype.I;ji.prototype.once=ji.prototype.L;ji.prototype.un=ji.prototype.J;ji.prototype.unByKey=ji.prototype.M;bu.prototype.type=bu.prototype.type;bu.prototype.target=bu.prototype.target;bu.prototype.preventDefault=bu.prototype.preventDefault;bu.prototype.stopPropagation=bu.prototype.stopPropagation;cu.prototype.getActive=cu.prototype.f;cu.prototype.getMap=cu.prototype.l;cu.prototype.setActive=cu.prototype.i;cu.prototype.get=cu.prototype.get;cu.prototype.getKeys=cu.prototype.P;
cu.prototype.getProperties=cu.prototype.N;cu.prototype.set=cu.prototype.set;cu.prototype.setProperties=cu.prototype.G;cu.prototype.unset=cu.prototype.S;cu.prototype.changed=cu.prototype.u;cu.prototype.dispatchEvent=cu.prototype.b;cu.prototype.getRevision=cu.prototype.K;cu.prototype.on=cu.prototype.I;cu.prototype.once=cu.prototype.L;cu.prototype.un=cu.prototype.J;cu.prototype.unByKey=cu.prototype.M;ki.prototype.getActive=ki.prototype.f;ki.prototype.getMap=ki.prototype.l;ki.prototype.setActive=ki.prototype.i;
ki.prototype.get=ki.prototype.get;ki.prototype.getKeys=ki.prototype.P;ki.prototype.getProperties=ki.prototype.N;ki.prototype.set=ki.prototype.set;ki.prototype.setProperties=ki.prototype.G;ki.prototype.unset=ki.prototype.S;ki.prototype.changed=ki.prototype.u;ki.prototype.dispatchEvent=ki.prototype.b;ki.prototype.getRevision=ki.prototype.K;ki.prototype.on=ki.prototype.I;ki.prototype.once=ki.prototype.L;ki.prototype.un=ki.prototype.J;ki.prototype.unByKey=ki.prototype.M;mi.prototype.getActive=mi.prototype.f;
mi.prototype.getMap=mi.prototype.l;mi.prototype.setActive=mi.prototype.i;mi.prototype.get=mi.prototype.get;mi.prototype.getKeys=mi.prototype.P;mi.prototype.getProperties=mi.prototype.N;mi.prototype.set=mi.prototype.set;mi.prototype.setProperties=mi.prototype.G;mi.prototype.unset=mi.prototype.S;mi.prototype.changed=mi.prototype.u;mi.prototype.dispatchEvent=mi.prototype.b;mi.prototype.getRevision=mi.prototype.K;mi.prototype.on=mi.prototype.I;mi.prototype.once=mi.prototype.L;mi.prototype.un=mi.prototype.J;
mi.prototype.unByKey=mi.prototype.M;su.prototype.type=su.prototype.type;su.prototype.target=su.prototype.target;su.prototype.preventDefault=su.prototype.preventDefault;su.prototype.stopPropagation=su.prototype.stopPropagation;tu.prototype.getActive=tu.prototype.f;tu.prototype.getMap=tu.prototype.l;tu.prototype.setActive=tu.prototype.i;tu.prototype.get=tu.prototype.get;tu.prototype.getKeys=tu.prototype.P;tu.prototype.getProperties=tu.prototype.N;tu.prototype.set=tu.prototype.set;
tu.prototype.setProperties=tu.prototype.G;tu.prototype.unset=tu.prototype.S;tu.prototype.changed=tu.prototype.u;tu.prototype.dispatchEvent=tu.prototype.b;tu.prototype.getRevision=tu.prototype.K;tu.prototype.on=tu.prototype.I;tu.prototype.once=tu.prototype.L;tu.prototype.un=tu.prototype.J;tu.prototype.unByKey=tu.prototype.M;oi.prototype.getActive=oi.prototype.f;oi.prototype.getMap=oi.prototype.l;oi.prototype.setActive=oi.prototype.i;oi.prototype.get=oi.prototype.get;oi.prototype.getKeys=oi.prototype.P;
oi.prototype.getProperties=oi.prototype.N;oi.prototype.set=oi.prototype.set;oi.prototype.setProperties=oi.prototype.G;oi.prototype.unset=oi.prototype.S;oi.prototype.changed=oi.prototype.u;oi.prototype.dispatchEvent=oi.prototype.b;oi.prototype.getRevision=oi.prototype.K;oi.prototype.on=oi.prototype.I;oi.prototype.once=oi.prototype.L;oi.prototype.un=oi.prototype.J;oi.prototype.unByKey=oi.prototype.M;qi.prototype.getActive=qi.prototype.f;qi.prototype.getMap=qi.prototype.l;qi.prototype.setActive=qi.prototype.i;
qi.prototype.get=qi.prototype.get;qi.prototype.getKeys=qi.prototype.P;qi.prototype.getProperties=qi.prototype.N;qi.prototype.set=qi.prototype.set;qi.prototype.setProperties=qi.prototype.G;qi.prototype.unset=qi.prototype.S;qi.prototype.changed=qi.prototype.u;qi.prototype.dispatchEvent=qi.prototype.b;qi.prototype.getRevision=qi.prototype.K;qi.prototype.on=qi.prototype.I;qi.prototype.once=qi.prototype.L;qi.prototype.un=qi.prototype.J;qi.prototype.unByKey=qi.prototype.M;ui.prototype.getActive=ui.prototype.f;
ui.prototype.getMap=ui.prototype.l;ui.prototype.setActive=ui.prototype.i;ui.prototype.get=ui.prototype.get;ui.prototype.getKeys=ui.prototype.P;ui.prototype.getProperties=ui.prototype.N;ui.prototype.set=ui.prototype.set;ui.prototype.setProperties=ui.prototype.G;ui.prototype.unset=ui.prototype.S;ui.prototype.changed=ui.prototype.u;ui.prototype.dispatchEvent=ui.prototype.b;ui.prototype.getRevision=ui.prototype.K;ui.prototype.on=ui.prototype.I;ui.prototype.once=ui.prototype.L;ui.prototype.un=ui.prototype.J;
ui.prototype.unByKey=ui.prototype.M;Gu.prototype.type=Gu.prototype.type;Gu.prototype.target=Gu.prototype.target;Gu.prototype.preventDefault=Gu.prototype.preventDefault;Gu.prototype.stopPropagation=Gu.prototype.stopPropagation;Hu.prototype.getActive=Hu.prototype.f;Hu.prototype.getMap=Hu.prototype.l;Hu.prototype.setActive=Hu.prototype.i;Hu.prototype.get=Hu.prototype.get;Hu.prototype.getKeys=Hu.prototype.P;Hu.prototype.getProperties=Hu.prototype.N;Hu.prototype.set=Hu.prototype.set;
Hu.prototype.setProperties=Hu.prototype.G;Hu.prototype.unset=Hu.prototype.S;Hu.prototype.changed=Hu.prototype.u;Hu.prototype.dispatchEvent=Hu.prototype.b;Hu.prototype.getRevision=Hu.prototype.K;Hu.prototype.on=Hu.prototype.I;Hu.prototype.once=Hu.prototype.L;Hu.prototype.un=Hu.prototype.J;Hu.prototype.unByKey=Hu.prototype.M;Ku.prototype.getActive=Ku.prototype.f;Ku.prototype.getMap=Ku.prototype.l;Ku.prototype.setActive=Ku.prototype.i;Ku.prototype.get=Ku.prototype.get;Ku.prototype.getKeys=Ku.prototype.P;
Ku.prototype.getProperties=Ku.prototype.N;Ku.prototype.set=Ku.prototype.set;Ku.prototype.setProperties=Ku.prototype.G;Ku.prototype.unset=Ku.prototype.S;Ku.prototype.changed=Ku.prototype.u;Ku.prototype.dispatchEvent=Ku.prototype.b;Ku.prototype.getRevision=Ku.prototype.K;Ku.prototype.on=Ku.prototype.I;Ku.prototype.once=Ku.prototype.L;Ku.prototype.un=Ku.prototype.J;Ku.prototype.unByKey=Ku.prototype.M;Ou.prototype.type=Ou.prototype.type;Ou.prototype.target=Ou.prototype.target;
Ou.prototype.preventDefault=Ou.prototype.preventDefault;Ou.prototype.stopPropagation=Ou.prototype.stopPropagation;Pu.prototype.getActive=Pu.prototype.f;Pu.prototype.getMap=Pu.prototype.l;Pu.prototype.setActive=Pu.prototype.i;Pu.prototype.get=Pu.prototype.get;Pu.prototype.getKeys=Pu.prototype.P;Pu.prototype.getProperties=Pu.prototype.N;Pu.prototype.set=Pu.prototype.set;Pu.prototype.setProperties=Pu.prototype.G;Pu.prototype.unset=Pu.prototype.S;Pu.prototype.changed=Pu.prototype.u;
Pu.prototype.dispatchEvent=Pu.prototype.b;Pu.prototype.getRevision=Pu.prototype.K;Pu.prototype.on=Pu.prototype.I;Pu.prototype.once=Pu.prototype.L;Pu.prototype.un=Pu.prototype.J;Pu.prototype.unByKey=Pu.prototype.M;Qc.prototype.get=Qc.prototype.get;Qc.prototype.getKeys=Qc.prototype.P;Qc.prototype.getProperties=Qc.prototype.N;Qc.prototype.set=Qc.prototype.set;Qc.prototype.setProperties=Qc.prototype.G;Qc.prototype.unset=Qc.prototype.S;Qc.prototype.changed=Qc.prototype.u;Qc.prototype.dispatchEvent=Qc.prototype.b;
Qc.prototype.getRevision=Qc.prototype.K;Qc.prototype.on=Qc.prototype.I;Qc.prototype.once=Qc.prototype.L;Qc.prototype.un=Qc.prototype.J;Qc.prototype.unByKey=Qc.prototype.M;Sc.prototype.getClosestPoint=Sc.prototype.yb;Sc.prototype.intersectsCoordinate=Sc.prototype.jb;Sc.prototype.getExtent=Sc.prototype.C;Sc.prototype.rotate=Sc.prototype.rotate;Sc.prototype.scale=Sc.prototype.scale;Sc.prototype.simplify=Sc.prototype.Fb;Sc.prototype.transform=Sc.prototype.lb;Sc.prototype.get=Sc.prototype.get;
Sc.prototype.getKeys=Sc.prototype.P;Sc.prototype.getProperties=Sc.prototype.N;Sc.prototype.set=Sc.prototype.set;Sc.prototype.setProperties=Sc.prototype.G;Sc.prototype.unset=Sc.prototype.S;Sc.prototype.changed=Sc.prototype.u;Sc.prototype.dispatchEvent=Sc.prototype.b;Sc.prototype.getRevision=Sc.prototype.K;Sc.prototype.on=Sc.prototype.I;Sc.prototype.once=Sc.prototype.L;Sc.prototype.un=Sc.prototype.J;Sc.prototype.unByKey=Sc.prototype.M;st.prototype.getFirstCoordinate=st.prototype.Nb;
st.prototype.getLastCoordinate=st.prototype.Ob;st.prototype.getLayout=st.prototype.Pb;st.prototype.rotate=st.prototype.rotate;st.prototype.scale=st.prototype.scale;st.prototype.getClosestPoint=st.prototype.yb;st.prototype.intersectsCoordinate=st.prototype.jb;st.prototype.getExtent=st.prototype.C;st.prototype.simplify=st.prototype.Fb;st.prototype.get=st.prototype.get;st.prototype.getKeys=st.prototype.P;st.prototype.getProperties=st.prototype.N;st.prototype.set=st.prototype.set;
st.prototype.setProperties=st.prototype.G;st.prototype.unset=st.prototype.S;st.prototype.changed=st.prototype.u;st.prototype.dispatchEvent=st.prototype.b;st.prototype.getRevision=st.prototype.K;st.prototype.on=st.prototype.I;st.prototype.once=st.prototype.L;st.prototype.un=st.prototype.J;st.prototype.unByKey=st.prototype.M;qn.prototype.getClosestPoint=qn.prototype.yb;qn.prototype.intersectsCoordinate=qn.prototype.jb;qn.prototype.getExtent=qn.prototype.C;qn.prototype.rotate=qn.prototype.rotate;
qn.prototype.scale=qn.prototype.scale;qn.prototype.simplify=qn.prototype.Fb;qn.prototype.transform=qn.prototype.lb;qn.prototype.get=qn.prototype.get;qn.prototype.getKeys=qn.prototype.P;qn.prototype.getProperties=qn.prototype.N;qn.prototype.set=qn.prototype.set;qn.prototype.setProperties=qn.prototype.G;qn.prototype.unset=qn.prototype.S;qn.prototype.changed=qn.prototype.u;qn.prototype.dispatchEvent=qn.prototype.b;qn.prototype.getRevision=qn.prototype.K;qn.prototype.on=qn.prototype.I;
qn.prototype.once=qn.prototype.L;qn.prototype.un=qn.prototype.J;qn.prototype.unByKey=qn.prototype.M;jd.prototype.getFirstCoordinate=jd.prototype.Nb;jd.prototype.getLastCoordinate=jd.prototype.Ob;jd.prototype.getLayout=jd.prototype.Pb;jd.prototype.rotate=jd.prototype.rotate;jd.prototype.scale=jd.prototype.scale;jd.prototype.getClosestPoint=jd.prototype.yb;jd.prototype.intersectsCoordinate=jd.prototype.jb;jd.prototype.getExtent=jd.prototype.C;jd.prototype.simplify=jd.prototype.Fb;
jd.prototype.transform=jd.prototype.lb;jd.prototype.get=jd.prototype.get;jd.prototype.getKeys=jd.prototype.P;jd.prototype.getProperties=jd.prototype.N;jd.prototype.set=jd.prototype.set;jd.prototype.setProperties=jd.prototype.G;jd.prototype.unset=jd.prototype.S;jd.prototype.changed=jd.prototype.u;jd.prototype.dispatchEvent=jd.prototype.b;jd.prototype.getRevision=jd.prototype.K;jd.prototype.on=jd.prototype.I;jd.prototype.once=jd.prototype.L;jd.prototype.un=jd.prototype.J;jd.prototype.unByKey=jd.prototype.M;
P.prototype.getFirstCoordinate=P.prototype.Nb;P.prototype.getLastCoordinate=P.prototype.Ob;P.prototype.getLayout=P.prototype.Pb;P.prototype.rotate=P.prototype.rotate;P.prototype.scale=P.prototype.scale;P.prototype.getClosestPoint=P.prototype.yb;P.prototype.intersectsCoordinate=P.prototype.jb;P.prototype.getExtent=P.prototype.C;P.prototype.simplify=P.prototype.Fb;P.prototype.transform=P.prototype.lb;P.prototype.get=P.prototype.get;P.prototype.getKeys=P.prototype.P;P.prototype.getProperties=P.prototype.N;
P.prototype.set=P.prototype.set;P.prototype.setProperties=P.prototype.G;P.prototype.unset=P.prototype.S;P.prototype.changed=P.prototype.u;P.prototype.dispatchEvent=P.prototype.b;P.prototype.getRevision=P.prototype.K;P.prototype.on=P.prototype.I;P.prototype.once=P.prototype.L;P.prototype.un=P.prototype.J;P.prototype.unByKey=P.prototype.M;Q.prototype.getFirstCoordinate=Q.prototype.Nb;Q.prototype.getLastCoordinate=Q.prototype.Ob;Q.prototype.getLayout=Q.prototype.Pb;Q.prototype.rotate=Q.prototype.rotate;
Q.prototype.scale=Q.prototype.scale;Q.prototype.getClosestPoint=Q.prototype.yb;Q.prototype.intersectsCoordinate=Q.prototype.jb;Q.prototype.getExtent=Q.prototype.C;Q.prototype.simplify=Q.prototype.Fb;Q.prototype.transform=Q.prototype.lb;Q.prototype.get=Q.prototype.get;Q.prototype.getKeys=Q.prototype.P;Q.prototype.getProperties=Q.prototype.N;Q.prototype.set=Q.prototype.set;Q.prototype.setProperties=Q.prototype.G;Q.prototype.unset=Q.prototype.S;Q.prototype.changed=Q.prototype.u;
Q.prototype.dispatchEvent=Q.prototype.b;Q.prototype.getRevision=Q.prototype.K;Q.prototype.on=Q.prototype.I;Q.prototype.once=Q.prototype.L;Q.prototype.un=Q.prototype.J;Q.prototype.unByKey=Q.prototype.M;R.prototype.getFirstCoordinate=R.prototype.Nb;R.prototype.getLastCoordinate=R.prototype.Ob;R.prototype.getLayout=R.prototype.Pb;R.prototype.rotate=R.prototype.rotate;R.prototype.scale=R.prototype.scale;R.prototype.getClosestPoint=R.prototype.yb;R.prototype.intersectsCoordinate=R.prototype.jb;
R.prototype.getExtent=R.prototype.C;R.prototype.simplify=R.prototype.Fb;R.prototype.transform=R.prototype.lb;R.prototype.get=R.prototype.get;R.prototype.getKeys=R.prototype.P;R.prototype.getProperties=R.prototype.N;R.prototype.set=R.prototype.set;R.prototype.setProperties=R.prototype.G;R.prototype.unset=R.prototype.S;R.prototype.changed=R.prototype.u;R.prototype.dispatchEvent=R.prototype.b;R.prototype.getRevision=R.prototype.K;R.prototype.on=R.prototype.I;R.prototype.once=R.prototype.L;
R.prototype.un=R.prototype.J;R.prototype.unByKey=R.prototype.M;S.prototype.getFirstCoordinate=S.prototype.Nb;S.prototype.getLastCoordinate=S.prototype.Ob;S.prototype.getLayout=S.prototype.Pb;S.prototype.rotate=S.prototype.rotate;S.prototype.scale=S.prototype.scale;S.prototype.getClosestPoint=S.prototype.yb;S.prototype.intersectsCoordinate=S.prototype.jb;S.prototype.getExtent=S.prototype.C;S.prototype.simplify=S.prototype.Fb;S.prototype.transform=S.prototype.lb;S.prototype.get=S.prototype.get;
S.prototype.getKeys=S.prototype.P;S.prototype.getProperties=S.prototype.N;S.prototype.set=S.prototype.set;S.prototype.setProperties=S.prototype.G;S.prototype.unset=S.prototype.S;S.prototype.changed=S.prototype.u;S.prototype.dispatchEvent=S.prototype.b;S.prototype.getRevision=S.prototype.K;S.prototype.on=S.prototype.I;S.prototype.once=S.prototype.L;S.prototype.un=S.prototype.J;S.prototype.unByKey=S.prototype.M;C.prototype.getFirstCoordinate=C.prototype.Nb;C.prototype.getLastCoordinate=C.prototype.Ob;
C.prototype.getLayout=C.prototype.Pb;C.prototype.rotate=C.prototype.rotate;C.prototype.scale=C.prototype.scale;C.prototype.getClosestPoint=C.prototype.yb;C.prototype.intersectsCoordinate=C.prototype.jb;C.prototype.getExtent=C.prototype.C;C.prototype.simplify=C.prototype.Fb;C.prototype.transform=C.prototype.lb;C.prototype.get=C.prototype.get;C.prototype.getKeys=C.prototype.P;C.prototype.getProperties=C.prototype.N;C.prototype.set=C.prototype.set;C.prototype.setProperties=C.prototype.G;
C.prototype.unset=C.prototype.S;C.prototype.changed=C.prototype.u;C.prototype.dispatchEvent=C.prototype.b;C.prototype.getRevision=C.prototype.K;C.prototype.on=C.prototype.I;C.prototype.once=C.prototype.L;C.prototype.un=C.prototype.J;C.prototype.unByKey=C.prototype.M;D.prototype.getFirstCoordinate=D.prototype.Nb;D.prototype.getLastCoordinate=D.prototype.Ob;D.prototype.getLayout=D.prototype.Pb;D.prototype.rotate=D.prototype.rotate;D.prototype.scale=D.prototype.scale;D.prototype.getClosestPoint=D.prototype.yb;
D.prototype.intersectsCoordinate=D.prototype.jb;D.prototype.getExtent=D.prototype.C;D.prototype.simplify=D.prototype.Fb;D.prototype.transform=D.prototype.lb;D.prototype.get=D.prototype.get;D.prototype.getKeys=D.prototype.P;D.prototype.getProperties=D.prototype.N;D.prototype.set=D.prototype.set;D.prototype.setProperties=D.prototype.G;D.prototype.unset=D.prototype.S;D.prototype.changed=D.prototype.u;D.prototype.dispatchEvent=D.prototype.b;D.prototype.getRevision=D.prototype.K;D.prototype.on=D.prototype.I;
D.prototype.once=D.prototype.L;D.prototype.un=D.prototype.J;D.prototype.unByKey=D.prototype.M;Pn.prototype.readFeatures=Pn.prototype.Fa;Yn.prototype.readFeatures=Yn.prototype.Fa;Pn.prototype.readFeatures=Pn.prototype.Fa;jf.prototype.get=jf.prototype.get;jf.prototype.getKeys=jf.prototype.P;jf.prototype.getProperties=jf.prototype.N;jf.prototype.set=jf.prototype.set;jf.prototype.setProperties=jf.prototype.G;jf.prototype.unset=jf.prototype.S;jf.prototype.changed=jf.prototype.u;
jf.prototype.dispatchEvent=jf.prototype.b;jf.prototype.getRevision=jf.prototype.K;jf.prototype.on=jf.prototype.I;jf.prototype.once=jf.prototype.L;jf.prototype.un=jf.prototype.J;jf.prototype.unByKey=jf.prototype.M;kf.prototype.getMap=kf.prototype.i;kf.prototype.setMap=kf.prototype.setMap;kf.prototype.setTarget=kf.prototype.c;kf.prototype.get=kf.prototype.get;kf.prototype.getKeys=kf.prototype.P;kf.prototype.getProperties=kf.prototype.N;kf.prototype.set=kf.prototype.set;kf.prototype.setProperties=kf.prototype.G;
kf.prototype.unset=kf.prototype.S;kf.prototype.changed=kf.prototype.u;kf.prototype.dispatchEvent=kf.prototype.b;kf.prototype.getRevision=kf.prototype.K;kf.prototype.on=kf.prototype.I;kf.prototype.once=kf.prototype.L;kf.prototype.un=kf.prototype.J;kf.prototype.unByKey=kf.prototype.M;nf.prototype.getMap=nf.prototype.i;nf.prototype.setMap=nf.prototype.setMap;nf.prototype.setTarget=nf.prototype.c;nf.prototype.get=nf.prototype.get;nf.prototype.getKeys=nf.prototype.P;nf.prototype.getProperties=nf.prototype.N;
nf.prototype.set=nf.prototype.set;nf.prototype.setProperties=nf.prototype.G;nf.prototype.unset=nf.prototype.S;nf.prototype.changed=nf.prototype.u;nf.prototype.dispatchEvent=nf.prototype.b;nf.prototype.getRevision=nf.prototype.K;nf.prototype.on=nf.prototype.I;nf.prototype.once=nf.prototype.L;nf.prototype.un=nf.prototype.J;nf.prototype.unByKey=nf.prototype.M;xf.prototype.getMap=xf.prototype.i;xf.prototype.setMap=xf.prototype.setMap;xf.prototype.setTarget=xf.prototype.c;xf.prototype.get=xf.prototype.get;
xf.prototype.getKeys=xf.prototype.P;xf.prototype.getProperties=xf.prototype.N;xf.prototype.set=xf.prototype.set;xf.prototype.setProperties=xf.prototype.G;xf.prototype.unset=xf.prototype.S;xf.prototype.changed=xf.prototype.u;xf.prototype.dispatchEvent=xf.prototype.b;xf.prototype.getRevision=xf.prototype.K;xf.prototype.on=xf.prototype.I;xf.prototype.once=xf.prototype.L;xf.prototype.un=xf.prototype.J;xf.prototype.unByKey=xf.prototype.M;em.prototype.getMap=em.prototype.i;em.prototype.setMap=em.prototype.setMap;
em.prototype.setTarget=em.prototype.c;em.prototype.get=em.prototype.get;em.prototype.getKeys=em.prototype.P;em.prototype.getProperties=em.prototype.N;em.prototype.set=em.prototype.set;em.prototype.setProperties=em.prototype.G;em.prototype.unset=em.prototype.S;em.prototype.changed=em.prototype.u;em.prototype.dispatchEvent=em.prototype.b;em.prototype.getRevision=em.prototype.K;em.prototype.on=em.prototype.I;em.prototype.once=em.prototype.L;em.prototype.un=em.prototype.J;em.prototype.unByKey=em.prototype.M;
sf.prototype.getMap=sf.prototype.i;sf.prototype.setMap=sf.prototype.setMap;sf.prototype.setTarget=sf.prototype.c;sf.prototype.get=sf.prototype.get;sf.prototype.getKeys=sf.prototype.P;sf.prototype.getProperties=sf.prototype.N;sf.prototype.set=sf.prototype.set;sf.prototype.setProperties=sf.prototype.G;sf.prototype.unset=sf.prototype.S;sf.prototype.changed=sf.prototype.u;sf.prototype.dispatchEvent=sf.prototype.b;sf.prototype.getRevision=sf.prototype.K;sf.prototype.on=sf.prototype.I;
sf.prototype.once=sf.prototype.L;sf.prototype.un=sf.prototype.J;sf.prototype.unByKey=sf.prototype.M;jm.prototype.getMap=jm.prototype.i;jm.prototype.setMap=jm.prototype.setMap;jm.prototype.setTarget=jm.prototype.c;jm.prototype.get=jm.prototype.get;jm.prototype.getKeys=jm.prototype.P;jm.prototype.getProperties=jm.prototype.N;jm.prototype.set=jm.prototype.set;jm.prototype.setProperties=jm.prototype.G;jm.prototype.unset=jm.prototype.S;jm.prototype.changed=jm.prototype.u;jm.prototype.dispatchEvent=jm.prototype.b;
jm.prototype.getRevision=jm.prototype.K;jm.prototype.on=jm.prototype.I;jm.prototype.once=jm.prototype.L;jm.prototype.un=jm.prototype.J;jm.prototype.unByKey=jm.prototype.M;vf.prototype.getMap=vf.prototype.i;vf.prototype.setMap=vf.prototype.setMap;vf.prototype.setTarget=vf.prototype.c;vf.prototype.get=vf.prototype.get;vf.prototype.getKeys=vf.prototype.P;vf.prototype.getProperties=vf.prototype.N;vf.prototype.set=vf.prototype.set;vf.prototype.setProperties=vf.prototype.G;vf.prototype.unset=vf.prototype.S;
vf.prototype.changed=vf.prototype.u;vf.prototype.dispatchEvent=vf.prototype.b;vf.prototype.getRevision=vf.prototype.K;vf.prototype.on=vf.prototype.I;vf.prototype.once=vf.prototype.L;vf.prototype.un=vf.prototype.J;vf.prototype.unByKey=vf.prototype.M;tm.prototype.getMap=tm.prototype.i;tm.prototype.setMap=tm.prototype.setMap;tm.prototype.setTarget=tm.prototype.c;tm.prototype.get=tm.prototype.get;tm.prototype.getKeys=tm.prototype.P;tm.prototype.getProperties=tm.prototype.N;tm.prototype.set=tm.prototype.set;
tm.prototype.setProperties=tm.prototype.G;tm.prototype.unset=tm.prototype.S;tm.prototype.changed=tm.prototype.u;tm.prototype.dispatchEvent=tm.prototype.b;tm.prototype.getRevision=tm.prototype.K;tm.prototype.on=tm.prototype.I;tm.prototype.once=tm.prototype.L;tm.prototype.un=tm.prototype.J;tm.prototype.unByKey=tm.prototype.M;ym.prototype.getMap=ym.prototype.i;ym.prototype.setMap=ym.prototype.setMap;ym.prototype.setTarget=ym.prototype.c;ym.prototype.get=ym.prototype.get;ym.prototype.getKeys=ym.prototype.P;
ym.prototype.getProperties=ym.prototype.N;ym.prototype.set=ym.prototype.set;ym.prototype.setProperties=ym.prototype.G;ym.prototype.unset=ym.prototype.S;ym.prototype.changed=ym.prototype.u;ym.prototype.dispatchEvent=ym.prototype.b;ym.prototype.getRevision=ym.prototype.K;ym.prototype.on=ym.prototype.I;ym.prototype.once=ym.prototype.L;ym.prototype.un=ym.prototype.J;ym.prototype.unByKey=ym.prototype.M;
  return OPENLAYERS.ol;
}));


(function() {
  'use strict';
  angular.module('ngMask', []);
})();(function() {
  'use strict';
  angular.module('ngMask')
    .directive('mask', ['$log', '$timeout', 'MaskService', function($log, $timeout, MaskService) {
      return {
        restrict: 'A',
        require: 'ngModel',
        compile: function($element, $attrs) { 
         if (!$attrs.mask || !$attrs.ngModel) {
            $log.info('Mask and ng-model attributes are required!');
            return;
          }

          var maskService = MaskService.create();
          var timeout;
          var promise;

          function setSelectionRange(selectionStart){
            if (typeof selectionStart !== 'number') {
              return;
            }

            // using $timeout:
            // it should run after the DOM has been manipulated by Angular
            // and after the browser renders (which may cause flicker in some cases)
            $timeout.cancel(timeout);
            timeout = $timeout(function(){
              var selectionEnd = selectionStart + 1;
              var input = $element[0];

              if (input.setSelectionRange) {
                input.focus();
                input.setSelectionRange(selectionStart, selectionEnd);
              } else if (input.createTextRange) {
                var range = input.createTextRange();

                range.collapse(true);
                range.moveEnd('character', selectionEnd);
                range.moveStart('character', selectionStart);
                range.select();
              }
            });
          }

          return {
            pre: function($scope, $element, $attrs, controller) {
              promise = maskService.generateRegex({
                mask: $attrs.mask,
                // repeat mask expression n times
                repeat: ($attrs.repeat || $attrs.maskRepeat),
                // clean model value - without divisors
                clean: (($attrs.clean || $attrs.maskClean) === 'true'),
                // limit length based on mask length
                limit: (($attrs.limit || $attrs.maskLimit || 'true') === 'true'),
                // how to act with a wrong value
                restrict: ($attrs.restrict || $attrs.maskRestrict || 'select'), //select, reject, accept
                // set validity mask
                validate: (($attrs.validate || $attrs.maskValidate || 'true') === 'true'),
                // default model value
                model: $attrs.ngModel,
                // default input value
                value: $attrs.ngValue
              });
            },
            post: function($scope, $element, $attrs, controller) {
              var timeout;
              var options = maskService.getOptions();

              function parseViewValue(value) {
                var untouchedValue = value;
                options = maskService.getOptions();
                // set default value equal 0
                value = value || '';

                // get view value object
                var viewValue = maskService.getViewValue(value);

                // get mask without question marks
                var maskWithoutOptionals = options['maskWithoutOptionals'] || '';

                // get view values capped
                // used on view
                var viewValueWithDivisors = viewValue.withDivisors(true);
                // used on model
                var viewValueWithoutDivisors = viewValue.withoutDivisors(true);

                try {
                  // get current regex
                  var regex = maskService.getRegex(viewValueWithDivisors.length - 1);
                  var fullRegex = maskService.getRegex(maskWithoutOptionals.length - 1);

                  // current position is valid
                  var validCurrentPosition = regex.test(viewValueWithDivisors) || fullRegex.test(viewValueWithDivisors);

                  // difference means for select option
                  var diffValueAndViewValueLengthIsOne = (value.length - viewValueWithDivisors.length) === 1;
                  var diffMaskAndViewValueIsGreaterThanZero = (maskWithoutOptionals.length - viewValueWithDivisors.length) > 0;

                  if (options.restrict !== 'accept') {
                    if (options.restrict === 'select' && (!validCurrentPosition || diffValueAndViewValueLengthIsOne)) {
                      var lastCharInputed = value[(value.length-1)];
                      var lastCharGenerated = viewValueWithDivisors[(viewValueWithDivisors.length-1)];

                      if ((lastCharInputed !== lastCharGenerated) && diffMaskAndViewValueIsGreaterThanZero) {
                        viewValueWithDivisors = viewValueWithDivisors + lastCharInputed;
                      }

                      var wrongPosition = maskService.getFirstWrongPosition(viewValueWithDivisors);
                      if (angular.isDefined(wrongPosition)) {
                        setSelectionRange(wrongPosition);
                      }
                    } else if (options.restrict === 'reject' && !validCurrentPosition) {
                      viewValue = maskService.removeWrongPositions(viewValueWithDivisors);
                      viewValueWithDivisors = viewValue.withDivisors(true);
                      viewValueWithoutDivisors = viewValue.withoutDivisors(true);

                      // setSelectionRange(viewValueWithDivisors.length);
                    }
                  }

                  if (!options.limit) {
                    viewValueWithDivisors = viewValue.withDivisors(false);
                    viewValueWithoutDivisors = viewValue.withoutDivisors(false);
                  }

                  // Set validity
                  if (options.validate && controller.$dirty) {
                    if (fullRegex.test(viewValueWithDivisors) || controller.$isEmpty(untouchedValue)) {
                      controller.$setValidity('mask', true);
                    } else {
                      controller.$setValidity('mask', false);
                    }
                  }

                  // Update view and model values
                  if(value !== viewValueWithDivisors){
                    controller.$setViewValue(angular.copy(viewValueWithDivisors), 'input');
                    controller.$render();
                  }
                } catch (e) {
                  $log.error('[mask - parseViewValue]');
                  throw e;
                }

                // Update model, can be different of view value
                if (options.clean) {
                  return viewValueWithoutDivisors;
                } else {
                  return viewValueWithDivisors;
                }
              }

              var callParseViewValue = function() {
                parseViewValue();

                controller.$parsers.push(parseViewValue);

                // $evalAsync from a directive
                // it should run after the DOM has been manipulated by Angular
                // but before the browser renders
                if(options.value) {
                  $scope.$evalAsync(function($scope) {
                    controller.$setViewValue(angular.copy(options.value), 'input');
                    controller.$render();
                  });
                }
              }

              $element.on('click input paste keyup', function() {
                timeout = $timeout(function() {
                  // Manual debounce to prevent multiple execution
                  $timeout.cancel(timeout);

                  parseViewValue($element.val());
                  $scope.$apply();
                }, 100);
              });

              // Register the watch to observe remote loading or promised data
              // Deregister calling returned function
              var watcher = $scope.$watch($attrs.ngModel, function (newValue, oldValue) {
                if (angular.isDefined(newValue)) {
                  parseViewValue(newValue);
                  watcher();
                }
              });

              $scope.$watch(function () {
                return [$attrs.mask];
              }, function() {
                promise = maskService.generateRegex({
                  mask: $attrs.mask,
                  // repeat mask expression n times
                  repeat: ($attrs.repeat || $attrs.maskRepeat),
                  // clean model value - without divisors
                  clean: (($attrs.clean || $attrs.maskClean) === 'true'),
                  // limit length based on mask length
                  limit: (($attrs.limit || $attrs.maskLimit || 'true') === 'true'),
                  // how to act with a wrong value
                  restrict: ($attrs.restrict || $attrs.maskRestrict || 'select'), //select, reject, accept
                  // set validity mask
                  validate: (($attrs.validate || $attrs.maskValidate || 'true') === 'true'),
                  // default model value
                  model: $attrs.ngModel,
                  // default input value
                  value: $attrs.ngValue
                }).then(function() {
                  $element.triggerHandler('click');
                });

                promise.then(callParseViewValue);
              }, true);

              promise.then(callParseViewValue);
            }
          }
        }
      }
    }]);
})();
(function() {
  'use strict';
  angular.module('ngMask')
    .factory('MaskService', ['$q', 'OptionalService', 'UtilService', function($q, OptionalService, UtilService) {
      function create() {
        var options;
        var maskWithoutOptionals;
        var maskWithoutOptionalsLength = 0;
        var maskWithoutOptionalsAndDivisorsLength = 0;
        var optionalIndexes = [];
        var optionalDivisors = {};
        var optionalDivisorsCombinations = [];
        var divisors = [];
        var divisorElements = {};
        var regex = [];
        var patterns = {
          '9': /[0-9]/,
          '8': /[0-8]/,
          '7': /[0-7]/,
          '6': /[0-6]/,
          '5': /[0-5]/,
          '4': /[0-4]/,
          '3': /[0-3]/,
          '2': /[0-2]/,
          '1': /[0-1]/,
          '0': /[0]/,
          '*': /./,
          'w': /\w/,
          'W': /\W/,
          'd': /\d/,
          'D': /\D/,
          's': /\s/,
          'S': /\S/,
          'b': /\b/,
          'A': /[A-Z]/,
          'a': /[a-z]/,
          'Z': /[A-ZÇÀÁÂÃÈÉÊẼÌÍÎĨÒÓÔÕÙÚÛŨ]/,
          'z': /[a-zçáàãâéèêẽíìĩîóòôõúùũüû]/,
          '@': /[a-zA-Z]/,
          '#': /[a-zA-ZçáàãâéèêẽíìĩîóòôõúùũüûÇÀÁÂÃÈÉÊẼÌÍÎĨÒÓÔÕÙÚÛŨ]/,
          '%': /[0-9a-zA-ZçáàãâéèêẽíìĩîóòôõúùũüûÇÀÁÂÃÈÉÊẼÌÍÎĨÒÓÔÕÙÚÛŨ]/
        };

        // REGEX

        function generateIntermetiateElementRegex(i, forceOptional) {
          var charRegex;
          try {
            var element = maskWithoutOptionals[i];
            var elementRegex = patterns[element];
            var hasOptional = isOptional(i);

            if (elementRegex) {
              charRegex = '(' + elementRegex.source + ')';
            } else { // is a divisor
              if (!isDivisor(i)) {
                divisors.push(i);
                divisorElements[i] = element;
              }

              charRegex = '(' + '\\' + element + ')';
            }
          } catch (e) {
            throw e;
          }

          if (hasOptional || forceOptional) {
            charRegex += '?';
          }

          return new RegExp(charRegex);
        }

        function generateIntermetiateRegex(i, forceOptional) {


          var elementRegex
          var elementOptionalRegex;
          try {
            var intermetiateElementRegex = generateIntermetiateElementRegex(i, forceOptional);
            elementRegex = intermetiateElementRegex;

            var hasOptional = isOptional(i);
            var currentRegex = intermetiateElementRegex.source;

            if (hasOptional && ((i+1) < maskWithoutOptionalsLength)) {
              var intermetiateRegex = generateIntermetiateRegex((i+1), true).elementOptionalRegex();
              currentRegex += intermetiateRegex.source;
            }

            elementOptionalRegex = new RegExp(currentRegex);
          } catch (e) {
            throw e;
          }
          return {
            elementRegex: function() {
              return elementRegex;
            },
            elementOptionalRegex: function() {
              // from element regex, gets the flow of regex until first not optional
              return elementOptionalRegex;
            }
          };
        }

        function generateRegex(opts) {
          var deferred = $q.defer();
          maskWithoutOptionals = null;
          maskWithoutOptionalsLength = 0;
          maskWithoutOptionalsAndDivisorsLength = 0;
          optionalIndexes = [];
          optionalDivisors = {};
          optionalDivisorsCombinations = [];
          divisors = [];
          divisorElements = {};
          regex = [];
          options = opts;

          try {
            var mask = opts['mask'];
            var repeat = opts['repeat'];

            if (!mask)
              return;

            if (repeat) {
              mask = Array((parseInt(repeat)+1)).join(mask);
            }

            optionalIndexes = OptionalService.getOptionals(mask).fromMaskWithoutOptionals();
            options['maskWithoutOptionals'] = maskWithoutOptionals = OptionalService.removeOptionals(mask);
            maskWithoutOptionalsLength = maskWithoutOptionals.length;

            var cumulativeRegex;
            for (var i=0; i<maskWithoutOptionalsLength; i++) {
              var charRegex = generateIntermetiateRegex(i);
              var elementRegex = charRegex.elementRegex();
              var elementOptionalRegex = charRegex.elementOptionalRegex();

              var newRegex = cumulativeRegex ? cumulativeRegex.source + elementOptionalRegex.source : elementOptionalRegex.source;
              newRegex = new RegExp(newRegex);
              cumulativeRegex = cumulativeRegex ? cumulativeRegex.source + elementRegex.source : elementRegex.source;
              cumulativeRegex = new RegExp(cumulativeRegex);

              regex.push(newRegex);
            }

            generateOptionalDivisors();
            maskWithoutOptionalsAndDivisorsLength = removeDivisors(maskWithoutOptionals).length;

            deferred.resolve({
              options: options,
              divisors: divisors,
              divisorElements: divisorElements,
              optionalIndexes: optionalIndexes,
              optionalDivisors: optionalDivisors,
              optionalDivisorsCombinations: optionalDivisorsCombinations
            });
          } catch (e) {
            deferred.reject(e);
            throw e;
          }

          return deferred.promise;
        }

        function getRegex(index) {
          var currentRegex;

          try {
            currentRegex = regex[index] ? regex[index].source : '';
          } catch (e) {
            throw e;
          }

          return (new RegExp('^' + currentRegex + '$'));
        }

        // DIVISOR

        function isOptional(currentPos) {
          return UtilService.inArray(currentPos, optionalIndexes);
        }

        function isDivisor(currentPos) {
          return UtilService.inArray(currentPos, divisors);
        }

        function generateOptionalDivisors() {
          function sortNumber(a,b) {
              return a - b;
          }

          var sortedDivisors = divisors.sort(sortNumber);
          var sortedOptionals = optionalIndexes.sort(sortNumber);
          for (var i = 0; i<sortedDivisors.length; i++) {
            var divisor = sortedDivisors[i];
            for (var j = 1; j<=sortedOptionals.length; j++) {
              var optional = sortedOptionals[(j-1)];
              if (optional >= divisor) {
                break;
              }

              if (optionalDivisors[divisor]) {
                optionalDivisors[divisor] = optionalDivisors[divisor].concat(divisor-j);
              } else {
                optionalDivisors[divisor] = [(divisor-j)];
              }

              // get the original divisor for alternative divisor
              divisorElements[(divisor-j)] = divisorElements[divisor];
            }
          }
        }

        function removeDivisors(value) {
              value = value.toString();
          try {
            if (divisors.length > 0 && value) {
              var keys = Object.keys(divisorElements);
              var elments = [];

              for (var i = keys.length - 1; i >= 0; i--) {
                var divisor = divisorElements[keys[i]];
                if (divisor) {
                  elments.push(divisor);
                }
              }

              elments = UtilService.uniqueArray(elments);

              // remove if it is not pattern
              var regex = new RegExp(('[' + '\\' + elments.join('\\') + ']'), 'g');
              return value.replace(regex, '');
            } else {
              return value;
            }
          } catch (e) {
            throw e;
          }
        }

        function insertDivisors(array, combination) {
          function insert(array, output) {
            var out = output;
            for (var i=0; i<array.length; i++) {
              var divisor = array[i];
              if (divisor < out.length) {
                out.splice(divisor, 0, divisorElements[divisor]);
              }
            }
            return out;
          }

          var output = array;
          var divs = divisors.filter(function(it) {
            var optionalDivisorsKeys = Object.keys(optionalDivisors).map(function(it){
              return parseInt(it);
            });

            return !UtilService.inArray(it, combination) && !UtilService.inArray(it, optionalDivisorsKeys);
          });

          if (!angular.isArray(array) || !angular.isArray(combination)) {
            return output;
          }

          // insert not optional divisors
          output = insert(divs, output);

          // insert optional divisors
          output = insert(combination, output);

          return output;
        }

        function tryDivisorConfiguration(value) {
          var output = value.split('');
          var defaultDivisors = true;

          // has optional?
          if (optionalIndexes.length > 0) {
            var lazyArguments = [];
            var optionalDivisorsKeys = Object.keys(optionalDivisors);

            // get all optional divisors as array of arrays [[], [], []...]
            for (var i=0; i<optionalDivisorsKeys.length; i++) {
              var val = optionalDivisors[optionalDivisorsKeys[i]];
              lazyArguments.push(val);
            }

            // generate all possible configurations
            if (optionalDivisorsCombinations.length === 0) {
              UtilService.lazyProduct(lazyArguments, function() {
                // convert arguments to array
                optionalDivisorsCombinations.push(Array.prototype.slice.call(arguments));
              });
            }

            for (var i = optionalDivisorsCombinations.length - 1; i >= 0; i--) {
              var outputClone = angular.copy(output);
              outputClone = insertDivisors(outputClone, optionalDivisorsCombinations[i]);

              // try validation
              var viewValueWithDivisors = outputClone.join('');
              var regex = getRegex(maskWithoutOptionals.length - 1);

              if (regex.test(viewValueWithDivisors)) {
                defaultDivisors = false;
                output = outputClone;
                break;
              }
            }
          }

          if (defaultDivisors) {
            output = insertDivisors(output, divisors);
          }

          return output.join('');
        }

        // MASK

        function getOptions() {
          return options;
        }

        function getViewValue(value) {
          try {
            var outputWithoutDivisors = removeDivisors(value);
            var output = tryDivisorConfiguration(outputWithoutDivisors);

            return {
              withDivisors: function(capped) {
                if (capped) {
                  return output.substr(0, maskWithoutOptionalsLength);
                } else {
                  return output;
                }
              },
              withoutDivisors: function(capped) {
                if (capped) {
                  return outputWithoutDivisors.substr(0, maskWithoutOptionalsAndDivisorsLength);
                } else {
                  return outputWithoutDivisors;
                }
              }
            };
          } catch (e) {
            throw e;
          }
        }

        // SELECTOR

        function getWrongPositions(viewValueWithDivisors, onlyFirst) {
          var pos = [];

          if (!viewValueWithDivisors) {
            return 0;
          }

          for (var i=0; i<viewValueWithDivisors.length; i++){
            var pattern = getRegex(i);
            var value = viewValueWithDivisors.substr(0, (i+1));

            if(pattern && !pattern.test(value)){
              pos.push(i);

              if (onlyFirst) {
                break;
              }
            }
          }

          return pos;
        }

        function getFirstWrongPosition(viewValueWithDivisors) {
          return getWrongPositions(viewValueWithDivisors, true)[0];
        }

        function removeWrongPositions(viewValueWithDivisors) {
          var wrongPositions = getWrongPositions(viewValueWithDivisors, false);
          var newViewValue = viewValueWithDivisors;

          for(var i = 0; i < wrongPositions.length; i++){
            var wrongPosition = wrongPositions[i];
            var viewValueArray = viewValueWithDivisors.split('');
            viewValueArray.splice(wrongPosition, 1);
            newViewValue = viewValueArray.join('');
          }

          return getViewValue(newViewValue);
        }

        return {
          getViewValue: getViewValue,
          generateRegex: generateRegex,
          getRegex: getRegex,
          getOptions: getOptions,
          removeDivisors: removeDivisors,
          getFirstWrongPosition: getFirstWrongPosition,
          removeWrongPositions: removeWrongPositions
        }
      }

      return {
        create: create
      }
    }]);
})();
(function() {
  'use strict';
  angular.module('ngMask')
    .factory('OptionalService', [function() {
      function getOptionalsIndexes(mask) {
        var indexes = [];

        try {
          var regexp = /\?/g;
          var match = [];

          while ((match = regexp.exec(mask)) != null) {
            // Save the optional char
            indexes.push((match.index - 1));
          }
        } catch (e) {
          throw e;
        }

        return {
          fromMask: function() {
            return indexes;
          },
          fromMaskWithoutOptionals: function() {
            return getOptionalsRelativeMaskWithoutOptionals(indexes);
          }
        };
      }

      function getOptionalsRelativeMaskWithoutOptionals(optionals) {
        var indexes = [];
        for (var i=0; i<optionals.length; i++) {
          indexes.push(optionals[i]-i);
        }
        return indexes;
      }

      function removeOptionals(mask) {
        var newMask;

        try {
          newMask = mask.replace(/\?/g, '');
        } catch (e) {
          throw e;
        }

        return newMask;
      }

      return {
        removeOptionals: removeOptionals,
        getOptionals: getOptionalsIndexes
      }
    }]);
})();(function() {
  'use strict';
  angular.module('ngMask')
    .factory('UtilService', [function() {

      // sets: an array of arrays
      // f: your callback function
      // context: [optional] the `this` to use for your callback
      // http://phrogz.net/lazy-cartesian-product
      function lazyProduct(sets, f, context){
        if (!context){
          context=this;
        }

        var p = [];
        var max = sets.length-1;
        var lens = [];

        for (var i=sets.length;i--;) {
          lens[i] = sets[i].length;
        }

        function dive(d){
          var a = sets[d];
          var len = lens[d];

          if (d === max) {
            for (var i=0;i<len;++i) {
              p[d] = a[i];
              f.apply(context, p);
            }
          } else {
            for (var i=0;i<len;++i) {
              p[d]=a[i];
              dive(d+1);
            }
          }

          p.pop();
        }

        dive(0);
      }

      function inArray(i, array) {
        var output;

        try {
          output = array.indexOf(i) > -1;
        } catch (e) {
          throw e;
        }

        return output;
      }

      function uniqueArray(array) {
        var u = {};
        var a = [];

        for (var i = 0, l = array.length; i < l; ++i) {
          if(u.hasOwnProperty(array[i])) {
            continue;
          }

          a.push(array[i]);
          u[array[i]] = 1;
        }

        return a;
      }

      return {
        lazyProduct: lazyProduct,
        inArray: inArray,
        uniqueArray: uniqueArray
      }
    }]);
})();
/**
 * Rangy, a cross-browser JavaScript range and selection library
 * https://github.com/timdown/rangy
 *
 * Copyright 2024, Tim Down
 * Licensed under the MIT license.
 * Version: 1.3.2
 * Build date: 2 November 2024
 */

(function(factory, root) {
    if (typeof define == "function" && define.amd) {
        // AMD. Register as an anonymous module.
        define(factory);
    } else if (typeof module != "undefined" && typeof exports == "object") {
        // Node/CommonJS style
        module.exports = factory();
    } else {
        // No AMD or CommonJS support so we place Rangy in (probably) the global variable
        root.rangy = factory();
    }
})(function() {

    var OBJECT = "object", FUNCTION = "function", UNDEFINED = "undefined";

    // Minimal set of properties required for DOM Level 2 Range compliance. Comparison constants such as START_TO_START
    // are omitted because ranges in KHTML do not have them but otherwise work perfectly well. See issue 113.
    var domRangeProperties = ["startContainer", "startOffset", "endContainer", "endOffset", "collapsed",
        "commonAncestorContainer"];

    // Minimal set of methods required for DOM Level 2 Range compliance
    var domRangeMethods = ["setStart", "setStartBefore", "setStartAfter", "setEnd", "setEndBefore",
        "setEndAfter", "collapse", "selectNode", "selectNodeContents", "compareBoundaryPoints", "deleteContents",
        "extractContents", "cloneContents", "insertNode", "surroundContents", "cloneRange", "toString", "detach"];

    var textRangeProperties = ["boundingHeight", "boundingLeft", "boundingTop", "boundingWidth", "htmlText", "text"];

    // Subset of TextRange's full set of methods that we're interested in
    var textRangeMethods = ["collapse", "compareEndPoints", "duplicate", "moveToElementText", "parentElement", "select",
        "setEndPoint", "getBoundingClientRect"];

    /*----------------------------------------------------------------------------------------------------------------*/

    // Trio of functions taken from Peter Michaux's article:
    // http://peter.michaux.ca/articles/feature-detection-state-of-the-art-browser-scripting
    function isHostMethod(o, p) {
        var t = typeof o[p];
        return t == FUNCTION || (!!(t == OBJECT && o[p])) || t == "unknown";
    }

    function isHostObject(o, p) {
        return !!(typeof o[p] == OBJECT && o[p]);
    }

    function isHostProperty(o, p) {
        return typeof o[p] != UNDEFINED;
    }

    // Creates a convenience function to save verbose repeated calls to tests functions
    function createMultiplePropertyTest(testFunc) {
        return function(o, props) {
            var i = props.length;
            while (i--) {
                if (!testFunc(o, props[i])) {
                    return false;
                }
            }
            return true;
        };
    }

    // Next trio of functions are a convenience to save verbose repeated calls to previous two functions
    var areHostMethods = createMultiplePropertyTest(isHostMethod);
    var areHostObjects = createMultiplePropertyTest(isHostObject);
    var areHostProperties = createMultiplePropertyTest(isHostProperty);

    function isTextRange(range) {
        return range && areHostMethods(range, textRangeMethods) && areHostProperties(range, textRangeProperties);
    }

    function getBody(doc) {
        return isHostObject(doc, "body") ? doc.body : doc.getElementsByTagName("body")[0];
    }

    var forEach = [].forEach ?
        function(arr, func) {
            arr.forEach(func);
        } :
        function(arr, func) {
            for (var i = 0, len = arr.length; i < len; ++i) {
                func(arr[i], i);
            }
        };

    var modules = {};

    var isBrowser = (typeof window != UNDEFINED && typeof document != UNDEFINED);

    var util = {
        isHostMethod: isHostMethod,
        isHostObject: isHostObject,
        isHostProperty: isHostProperty,
        areHostMethods: areHostMethods,
        areHostObjects: areHostObjects,
        areHostProperties: areHostProperties,
        isTextRange: isTextRange,
        getBody: getBody,
        forEach: forEach
    };

    var api = {
        version: "1.3.2",
        initialized: false,
        isBrowser: isBrowser,
        supported: true,
        util: util,
        features: {},
        modules: modules,
        config: {
            alertOnFail: false,
            alertOnWarn: false,
            preferTextRange: false,
            autoInitialize: (typeof rangyAutoInitialize == UNDEFINED) ? true : rangyAutoInitialize
        }
    };

    function consoleLog(msg) {
        if (typeof console != UNDEFINED && isHostMethod(console, "log")) {
            console.log(msg);
        }
    }

    function alertOrLog(msg, shouldAlert) {
        if (isBrowser && shouldAlert) {
            alert(msg);
        } else  {
            consoleLog(msg);
        }
    }

    function fail(reason) {
        api.initialized = true;
        api.supported = false;
        alertOrLog("Rangy is not supported in this environment. Reason: " + reason, api.config.alertOnFail);
    }

    api.fail = fail;

    function warn(msg) {
        alertOrLog("Rangy warning: " + msg, api.config.alertOnWarn);
    }

    api.warn = warn;

    // Add utility extend() method
    var extend;
    if ({}.hasOwnProperty) {
        util.extend = extend = function(obj, props, deep) {
            var o, p;
            for (var i in props) {
                if (i === "__proto__" || i === "constructor" || i === "prototype") {
                    continue;
                }
                if (props.hasOwnProperty(i)) {
                    o = obj[i];
                    p = props[i];
                    if (deep && o !== null && typeof o == "object" && p !== null && typeof p == "object") {
                        extend(o, p, true);
                    }
                    obj[i] = p;
                }
            }
            // Special case for toString, which does not show up in for...in loops in IE <= 8
            if (props.hasOwnProperty("toString")) {
                obj.toString = props.toString;
            }
            return obj;
        };

        util.createOptions = function(optionsParam, defaults) {
            var options = {};
            extend(options, defaults);
            if (optionsParam) {
                extend(options, optionsParam);
            }
            return options;
        };
    } else {
        fail("hasOwnProperty not supported");
    }

    // Test whether we're in a browser and bail out if not
    if (!isBrowser) {
        fail("Rangy can only run in a browser");
    }

    // Test whether Array.prototype.slice can be relied on for NodeLists and use an alternative toArray() if not
    (function() {
        var toArray;

        if (isBrowser) {
            var el = document.createElement("div");
            el.appendChild(document.createElement("span"));
            var slice = [].slice;
            try {
                if (slice.call(el.childNodes, 0)[0].nodeType == 1) {
                    toArray = function(arrayLike) {
                        return slice.call(arrayLike, 0);
                    };
                }
            } catch (e) {}
        }

        if (!toArray) {
            toArray = function(arrayLike) {
                var arr = [];
                for (var i = 0, len = arrayLike.length; i < len; ++i) {
                    arr[i] = arrayLike[i];
                }
                return arr;
            };
        }

        util.toArray = toArray;
    })();

    // Very simple event handler wrapper function that doesn't attempt to solve issues such as "this" handling or
    // normalization of event properties because we don't need this.
    var addListener;
    if (isBrowser) {
        if (isHostMethod(document, "addEventListener")) {
            addListener = function(obj, eventType, listener) {
                obj.addEventListener(eventType, listener, false);
            };
        } else if (isHostMethod(document, "attachEvent")) {
            addListener = function(obj, eventType, listener) {
                obj.attachEvent("on" + eventType, listener);
            };
        } else {
            fail("Document does not have required addEventListener or attachEvent method");
        }

        util.addListener = addListener;
    }

    var initListeners = [];

    function getErrorDesc(ex) {
        return ex.message || ex.description || String(ex);
    }

    // Initialization
    function init() {
        if (!isBrowser || api.initialized) {
            return;
        }
        var testRange;
        var implementsDomRange = false, implementsTextRange = false;

        // First, perform basic feature tests

        if (isHostMethod(document, "createRange")) {
            testRange = document.createRange();
            if (areHostMethods(testRange, domRangeMethods) && areHostProperties(testRange, domRangeProperties)) {
                implementsDomRange = true;
            }
        }

        var body = getBody(document);
        if (!body || body.nodeName.toLowerCase() != "body") {
            fail("No body element found");
            return;
        }

        if (body && isHostMethod(body, "createTextRange")) {
            testRange = body.createTextRange();
            if (isTextRange(testRange)) {
                implementsTextRange = true;
            }
        }

        if (!implementsDomRange && !implementsTextRange) {
            fail("Neither Range nor TextRange are available");
            return;
        }

        api.initialized = true;
        api.features = {
            implementsDomRange: implementsDomRange,
            implementsTextRange: implementsTextRange
        };

        // Initialize modules
        var module, errorMessage;
        for (var moduleName in modules) {
            if ( (module = modules[moduleName]) instanceof Module ) {
                module.init(module, api);
            }
        }

        // Call init listeners
        for (var i = 0, len = initListeners.length; i < len; ++i) {
            try {
                initListeners[i](api);
            } catch (ex) {
                errorMessage = "Rangy init listener threw an exception. Continuing. Detail: " + getErrorDesc(ex);
                consoleLog(errorMessage);
            }
        }
    }

    function deprecationNotice(deprecated, replacement, module) {
        if (module) {
            deprecated += " in module " + module.name;
        }
        api.warn("DEPRECATED: " + deprecated + " is deprecated. Please use " +
        replacement + " instead.");
    }

    function createAliasForDeprecatedMethod(owner, deprecated, replacement, module) {
        owner[deprecated] = function() {
            deprecationNotice(deprecated, replacement, module);
            return owner[replacement].apply(owner, util.toArray(arguments));
        };
    }

    util.deprecationNotice = deprecationNotice;
    util.createAliasForDeprecatedMethod = createAliasForDeprecatedMethod;

    // Allow external scripts to initialize this library in case it's loaded after the document has loaded
    api.init = init;

    // Execute listener immediately if already initialized
    api.addInitListener = function(listener) {
        if (api.initialized) {
            listener(api);
        } else {
            initListeners.push(listener);
        }
    };

    var shimListeners = [];

    api.addShimListener = function(listener) {
        shimListeners.push(listener);
    };

    function shim(win) {
        win = win || window;
        init();

        // Notify listeners
        for (var i = 0, len = shimListeners.length; i < len; ++i) {
            shimListeners[i](win);
        }
    }

    if (isBrowser) {
        api.shim = api.createMissingNativeApi = shim;
        createAliasForDeprecatedMethod(api, "createMissingNativeApi", "shim");
    }

    function Module(name, dependencies, initializer) {
        this.name = name;
        this.dependencies = dependencies;
        this.initialized = false;
        this.supported = false;
        this.initializer = initializer;
    }

    Module.prototype = {
        init: function() {
            var requiredModuleNames = this.dependencies || [];
            for (var i = 0, len = requiredModuleNames.length, requiredModule, moduleName; i < len; ++i) {
                moduleName = requiredModuleNames[i];

                requiredModule = modules[moduleName];
                if (!requiredModule || !(requiredModule instanceof Module)) {
                    throw new Error("required module '" + moduleName + "' not found");
                }

                requiredModule.init();

                if (!requiredModule.supported) {
                    throw new Error("required module '" + moduleName + "' not supported");
                }
            }

            // Now run initializer
            this.initializer(this);
        },

        fail: function(reason) {
            this.initialized = true;
            this.supported = false;
            throw new Error(reason);
        },

        warn: function(msg) {
            api.warn("Module " + this.name + ": " + msg);
        },

        deprecationNotice: function(deprecated, replacement) {
            api.warn("DEPRECATED: " + deprecated + " in module " + this.name + " is deprecated. Please use " +
                replacement + " instead");
        },

        createError: function(msg) {
            return new Error("Error in Rangy " + this.name + " module: " + msg);
        }
    };

    function createModule(name, dependencies, initFunc) {
        var newModule = new Module(name, dependencies, function(module) {
            if (!module.initialized) {
                module.initialized = true;
                try {
                    initFunc(api, module);
                    module.supported = true;
                } catch (ex) {
                    var errorMessage = "Module '" + name + "' failed to load: " + getErrorDesc(ex);
                    consoleLog(errorMessage);
                    if (ex.stack) {
                        consoleLog(ex.stack);
                    }
                }
            }
        });
        modules[name] = newModule;
        return newModule;
    }

    api.createModule = function(name) {
        // Allow 2 or 3 arguments (second argument is an optional array of dependencies)
        var initFunc, dependencies;
        if (arguments.length == 2) {
            initFunc = arguments[1];
            dependencies = [];
        } else {
            initFunc = arguments[2];
            dependencies = arguments[1];
        }

        var module = createModule(name, dependencies, initFunc);

        // Initialize the module immediately if the core is already initialized
        if (api.initialized && api.supported) {
            module.init();
        }
    };

    api.createCoreModule = function(name, dependencies, initFunc) {
        createModule(name, dependencies, initFunc);
    };

    /*----------------------------------------------------------------------------------------------------------------*/

    // Ensure rangy.rangePrototype and rangy.selectionPrototype are available immediately

    function RangePrototype() {}
    api.RangePrototype = RangePrototype;
    api.rangePrototype = new RangePrototype();

    function SelectionPrototype() {}
    api.selectionPrototype = new SelectionPrototype();

    /*----------------------------------------------------------------------------------------------------------------*/

    // DOM utility methods used by Rangy
    api.createCoreModule("DomUtil", [], function(api, module) {
        var UNDEF = "undefined";
        var util = api.util;
        var getBody = util.getBody;

        // Perform feature tests
        if (!util.areHostMethods(document, ["createDocumentFragment", "createElement", "createTextNode"])) {
            module.fail("document missing a Node creation method");
        }

        if (!util.isHostMethod(document, "getElementsByTagName")) {
            module.fail("document missing getElementsByTagName method");
        }

        var el = document.createElement("div");
        if (!util.areHostMethods(el, ["insertBefore", "appendChild", "cloneNode"] ||
                !util.areHostObjects(el, ["previousSibling", "nextSibling", "childNodes", "parentNode"]))) {
            module.fail("Incomplete Element implementation");
        }

        // innerHTML is required for Range's createContextualFragment method
        if (!util.isHostProperty(el, "innerHTML")) {
            module.fail("Element is missing innerHTML property");
        }

        var textNode = document.createTextNode("test");
        if (!util.areHostMethods(textNode, ["splitText", "deleteData", "insertData", "appendData", "cloneNode"] ||
                !util.areHostObjects(el, ["previousSibling", "nextSibling", "childNodes", "parentNode"]) ||
                !util.areHostProperties(textNode, ["data"]))) {
            module.fail("Incomplete Text Node implementation");
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        // Removed use of indexOf because of a bizarre bug in Opera that is thrown in one of the Acid3 tests. I haven't been
        // able to replicate it outside of the test. The bug is that indexOf returns -1 when called on an Array that
        // contains just the document as a single element and the value searched for is the document.
        var arrayContains = /*Array.prototype.indexOf ?
            function(arr, val) {
                return arr.indexOf(val) > -1;
            }:*/

            function(arr, val) {
                var i = arr.length;
                while (i--) {
                    if (arr[i] === val) {
                        return true;
                    }
                }
                return false;
            };

        // Opera 11 puts HTML elements in the null namespace, it seems, and IE 7 has undefined namespaceURI
        function isHtmlNamespace(node) {
            var ns;
            return typeof node.namespaceURI == UNDEF || ((ns = node.namespaceURI) === null || ns == "http://www.w3.org/1999/xhtml");
        }

        function parentElement(node) {
            var parent = node.parentNode;
            return (parent.nodeType == 1) ? parent : null;
        }

        function getNodeIndex(node) {
            var i = 0;
            while( (node = node.previousSibling) ) {
                ++i;
            }
            return i;
        }

        function getNodeLength(node) {
            switch (node.nodeType) {
                case 7:
                case 10:
                    return 0;
                case 3:
                case 8:
                    return node.length;
                default:
                    return node.childNodes.length;
            }
        }

        function getCommonAncestor(node1, node2) {
            var ancestors = [], n;
            for (n = node1; n; n = n.parentNode) {
                ancestors.push(n);
            }

            for (n = node2; n; n = n.parentNode) {
                if (arrayContains(ancestors, n)) {
                    return n;
                }
            }

            return null;
        }

        function isAncestorOf(ancestor, descendant, selfIsAncestor) {
            var n = selfIsAncestor ? descendant : descendant.parentNode;
            while (n) {
                if (n === ancestor) {
                    return true;
                } else {
                    n = n.parentNode;
                }
            }
            return false;
        }

        function isOrIsAncestorOf(ancestor, descendant) {
            return isAncestorOf(ancestor, descendant, true);
        }

        function getClosestAncestorIn(node, ancestor, selfIsAncestor) {
            var p, n = selfIsAncestor ? node : node.parentNode;
            while (n) {
                p = n.parentNode;
                if (p === ancestor) {
                    return n;
                }
                n = p;
            }
            return null;
        }

        function isCharacterDataNode(node) {
            var t = node.nodeType;
            return t == 3 || t == 4 || t == 8 ; // Text, CDataSection or Comment
        }

        function isTextOrCommentNode(node) {
            if (!node) {
                return false;
            }
            var t = node.nodeType;
            return t == 3 || t == 8 ; // Text or Comment
        }

        function insertAfter(node, precedingNode) {
            var nextNode = precedingNode.nextSibling, parent = precedingNode.parentNode;
            if (nextNode) {
                parent.insertBefore(node, nextNode);
            } else {
                parent.appendChild(node);
            }
            return node;
        }

        // Note that we cannot use splitText() because it is bugridden in IE 9.
        function splitDataNode(node, index, positionsToPreserve) {
            var newNode = node.cloneNode(false);
            newNode.deleteData(0, index);
            node.deleteData(index, node.length - index);
            insertAfter(newNode, node);

            // Preserve positions
            if (positionsToPreserve) {
                for (var i = 0, position; position = positionsToPreserve[i++]; ) {
                    // Handle case where position was inside the portion of node after the split point
                    if (position.node == node && position.offset > index) {
                        position.node = newNode;
                        position.offset -= index;
                    }
                    // Handle the case where the position is a node offset within node's parent
                    else if (position.node == node.parentNode && position.offset > getNodeIndex(node)) {
                        ++position.offset;
                    }
                }
            }
            return newNode;
        }

        function getDocument(node) {
            if (node.nodeType == 9) {
                return node;
            } else if (typeof node.ownerDocument != UNDEF) {
                return node.ownerDocument;
            } else if (typeof node.document != UNDEF) {
                return node.document;
            } else if (node.parentNode) {
                return getDocument(node.parentNode);
            } else {
                throw module.createError("getDocument: no document found for node");
            }
        }

        function getWindow(node) {
            var doc = getDocument(node);
            if (typeof doc.defaultView != UNDEF) {
                return doc.defaultView;
            } else if (typeof doc.parentWindow != UNDEF) {
                return doc.parentWindow;
            } else {
                throw module.createError("Cannot get a window object for node");
            }
        }

        function getIframeDocument(iframeEl) {
            if (typeof iframeEl.contentDocument != UNDEF) {
                return iframeEl.contentDocument;
            } else if (typeof iframeEl.contentWindow != UNDEF) {
                return iframeEl.contentWindow.document;
            } else {
                throw module.createError("getIframeDocument: No Document object found for iframe element");
            }
        }

        function getIframeWindow(iframeEl) {
            if (typeof iframeEl.contentWindow != UNDEF) {
                return iframeEl.contentWindow;
            } else if (typeof iframeEl.contentDocument != UNDEF) {
                return iframeEl.contentDocument.defaultView;
            } else {
                throw module.createError("getIframeWindow: No Window object found for iframe element");
            }
        }

        // This looks bad. Is it worth it?
        function isWindow(obj) {
            return obj && util.isHostMethod(obj, "setTimeout") && util.isHostObject(obj, "document");
        }

        function getContentDocument(obj, module, methodName) {
            var doc;

            if (!obj) {
                doc = document;
            }

            // Test if a DOM node has been passed and obtain a document object for it if so
            else if (util.isHostProperty(obj, "nodeType")) {
                doc = (obj.nodeType == 1 && obj.tagName.toLowerCase() == "iframe") ?
                    getIframeDocument(obj) : getDocument(obj);
            }

            // Test if the doc parameter appears to be a Window object
            else if (isWindow(obj)) {
                doc = obj.document;
            }

            if (!doc) {
                throw module.createError(methodName + "(): Parameter must be a Window object or DOM node");
            }

            return doc;
        }

        function getRootContainer(node) {
            var parent;
            while ( (parent = node.parentNode) ) {
                node = parent;
            }
            return node;
        }

        function comparePoints(nodeA, offsetA, nodeB, offsetB) {
            // See http://www.w3.org/TR/DOM-Level-2-Traversal-Range/ranges.html#Level-2-Range-Comparing
            var nodeC, root, childA, childB, n;
            if (nodeA == nodeB) {
                // Case 1: nodes are the same
                return offsetA === offsetB ? 0 : (offsetA < offsetB) ? -1 : 1;
            } else if ( (nodeC = getClosestAncestorIn(nodeB, nodeA, true)) ) {
                // Case 2: node C (container B or an ancestor) is a child node of A
                return offsetA <= getNodeIndex(nodeC) ? -1 : 1;
            } else if ( (nodeC = getClosestAncestorIn(nodeA, nodeB, true)) ) {
                // Case 3: node C (container A or an ancestor) is a child node of B
                return getNodeIndex(nodeC) < offsetB  ? -1 : 1;
            } else {
                root = getCommonAncestor(nodeA, nodeB);
                if (!root) {
                    throw new Error("comparePoints error: nodes have no common ancestor");
                }

                // Case 4: containers are siblings or descendants of siblings
                childA = (nodeA === root) ? root : getClosestAncestorIn(nodeA, root, true);
                childB = (nodeB === root) ? root : getClosestAncestorIn(nodeB, root, true);

                if (childA === childB) {
                    // This shouldn't be possible
                    throw module.createError("comparePoints got to case 4 and childA and childB are the same!");
                } else {
                    n = root.firstChild;
                    while (n) {
                        if (n === childA) {
                            return -1;
                        } else if (n === childB) {
                            return 1;
                        }
                        n = n.nextSibling;
                    }
                }
            }
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        // Test for IE's crash (IE 6/7) or exception (IE >= 8) when a reference to garbage-collected text node is queried
        var crashyTextNodes = false;

        function isBrokenNode(node) {
            var n;
            try {
                n = node.parentNode;
                return false;
            } catch (e) {
                return true;
            }
        }

        (function() {
            var el = document.createElement("b");
            el.innerHTML = "1";
            var textNode = el.firstChild;
            el.innerHTML = "<br />";
            crashyTextNodes = isBrokenNode(textNode);

            api.features.crashyTextNodes = crashyTextNodes;
        })();

        /*----------------------------------------------------------------------------------------------------------------*/

        function inspectNode(node) {
            if (!node) {
                return "[No node]";
            }
            if (crashyTextNodes && isBrokenNode(node)) {
                return "[Broken node]";
            }
            if (isCharacterDataNode(node)) {
                return '"' + node.data + '"';
            }
            if (node.nodeType == 1) {
                var idAttr = node.id ? ' id="' + node.id + '"' : "";
                return "<" + node.nodeName + idAttr + ">[index:" + getNodeIndex(node) + ",length:" + node.childNodes.length + "][" + (node.innerHTML || "[innerHTML not supported]").slice(0, 25) + "]";
            }
            return node.nodeName;
        }

        function fragmentFromNodeChildren(node) {
            var fragment = getDocument(node).createDocumentFragment(), child;
            while ( (child = node.firstChild) ) {
                fragment.appendChild(child);
            }
            return fragment;
        }

        var getComputedStyleProperty;
        if (typeof window.getComputedStyle != UNDEF) {
            getComputedStyleProperty = function(el, propName) {
                return getWindow(el).getComputedStyle(el, null)[propName];
            };
        } else if (typeof document.documentElement.currentStyle != UNDEF) {
            getComputedStyleProperty = function(el, propName) {
                return el.currentStyle ? el.currentStyle[propName] : "";
            };
        } else {
            module.fail("No means of obtaining computed style properties found");
        }

        function createTestElement(doc, html, contentEditable) {
            var body = getBody(doc);
            var el = doc.createElement("div");
            el.contentEditable = "" + !!contentEditable;
            if (html) {
                el.innerHTML = html;
            }

            // Insert the test element at the start of the body to prevent scrolling to the bottom in iOS (issue #292)
            var bodyFirstChild = body.firstChild;
            if (bodyFirstChild) {
                body.insertBefore(el, bodyFirstChild);
            } else {
                body.appendChild(el);
            }

            return el;
        }

        function removeNode(node) {
            return node.parentNode.removeChild(node);
        }

        function NodeIterator(root) {
            this.root = root;
            this._next = root;
        }

        NodeIterator.prototype = {
            _current: null,

            hasNext: function() {
                return !!this._next;
            },

            next: function() {
                var n = this._current = this._next;
                var child, next;
                if (this._current) {
                    child = n.firstChild;
                    if (child) {
                        this._next = child;
                    } else {
                        next = null;
                        while ((n !== this.root) && !(next = n.nextSibling)) {
                            n = n.parentNode;
                        }
                        this._next = next;
                    }
                }
                return this._current;
            },

            detach: function() {
                this._current = this._next = this.root = null;
            }
        };

        function createIterator(root) {
            return new NodeIterator(root);
        }

        function DomPosition(node, offset) {
            this.node = node;
            this.offset = offset;
        }

        DomPosition.prototype = {
            equals: function(pos) {
                return !!pos && this.node === pos.node && this.offset == pos.offset;
            },

            inspect: function() {
                return "[DomPosition(" + inspectNode(this.node) + ":" + this.offset + ")]";
            },

            toString: function() {
                return this.inspect();
            }
        };

        function DOMException(codeName) {
            this.code = this[codeName];
            this.codeName = codeName;
            this.message = "DOMException: " + this.codeName;
        }

        DOMException.prototype = {
            INDEX_SIZE_ERR: 1,
            HIERARCHY_REQUEST_ERR: 3,
            WRONG_DOCUMENT_ERR: 4,
            NO_MODIFICATION_ALLOWED_ERR: 7,
            NOT_FOUND_ERR: 8,
            NOT_SUPPORTED_ERR: 9,
            INVALID_STATE_ERR: 11,
            INVALID_NODE_TYPE_ERR: 24
        };

        DOMException.prototype.toString = function() {
            return this.message;
        };

        api.dom = {
            arrayContains: arrayContains,
            isHtmlNamespace: isHtmlNamespace,
            parentElement: parentElement,
            getNodeIndex: getNodeIndex,
            getNodeLength: getNodeLength,
            getCommonAncestor: getCommonAncestor,
            isAncestorOf: isAncestorOf,
            isOrIsAncestorOf: isOrIsAncestorOf,
            getClosestAncestorIn: getClosestAncestorIn,
            isCharacterDataNode: isCharacterDataNode,
            isTextOrCommentNode: isTextOrCommentNode,
            insertAfter: insertAfter,
            splitDataNode: splitDataNode,
            getDocument: getDocument,
            getWindow: getWindow,
            getIframeWindow: getIframeWindow,
            getIframeDocument: getIframeDocument,
            getBody: getBody,
            isWindow: isWindow,
            getContentDocument: getContentDocument,
            getRootContainer: getRootContainer,
            comparePoints: comparePoints,
            isBrokenNode: isBrokenNode,
            inspectNode: inspectNode,
            getComputedStyleProperty: getComputedStyleProperty,
            createTestElement: createTestElement,
            removeNode: removeNode,
            fragmentFromNodeChildren: fragmentFromNodeChildren,
            createIterator: createIterator,
            DomPosition: DomPosition
        };

        api.DOMException = DOMException;
    });

    /*----------------------------------------------------------------------------------------------------------------*/

    // Pure JavaScript implementation of DOM Range
    api.createCoreModule("DomRange", ["DomUtil"], function(api, module) {
        var dom = api.dom;
        var util = api.util;
        var DomPosition = dom.DomPosition;
        var DOMException = api.DOMException;

        var isCharacterDataNode = dom.isCharacterDataNode;
        var getNodeIndex = dom.getNodeIndex;
        var isOrIsAncestorOf = dom.isOrIsAncestorOf;
        var getDocument = dom.getDocument;
        var comparePoints = dom.comparePoints;
        var splitDataNode = dom.splitDataNode;
        var getClosestAncestorIn = dom.getClosestAncestorIn;
        var getNodeLength = dom.getNodeLength;
        var arrayContains = dom.arrayContains;
        var getRootContainer = dom.getRootContainer;
        var crashyTextNodes = api.features.crashyTextNodes;

        var removeNode = dom.removeNode;

        /*----------------------------------------------------------------------------------------------------------------*/

        // Utility functions

        function isNonTextPartiallySelected(node, range) {
            return (node.nodeType != 3) &&
                   (isOrIsAncestorOf(node, range.startContainer) || isOrIsAncestorOf(node, range.endContainer));
        }

        function getRangeDocument(range) {
            return range.document || getDocument(range.startContainer);
        }

        function getRangeRoot(range) {
            return getRootContainer(range.startContainer);
        }

        function getBoundaryBeforeNode(node) {
            return new DomPosition(node.parentNode, getNodeIndex(node));
        }

        function getBoundaryAfterNode(node) {
            return new DomPosition(node.parentNode, getNodeIndex(node) + 1);
        }

        function insertNodeAtPosition(node, n, o) {
            var firstNodeInserted = node.nodeType == 11 ? node.firstChild : node;
            if (isCharacterDataNode(n)) {
                if (o == n.length) {
                    dom.insertAfter(node, n);
                } else {
                    n.parentNode.insertBefore(node, o == 0 ? n : splitDataNode(n, o));
                }
            } else if (o >= n.childNodes.length) {
                n.appendChild(node);
            } else {
                n.insertBefore(node, n.childNodes[o]);
            }
            return firstNodeInserted;
        }

        function rangesIntersect(rangeA, rangeB, touchingIsIntersecting) {
            assertRangeValid(rangeA);
            assertRangeValid(rangeB);

            if (getRangeDocument(rangeB) != getRangeDocument(rangeA)) {
                throw new DOMException("WRONG_DOCUMENT_ERR");
            }

            var startComparison = comparePoints(rangeA.startContainer, rangeA.startOffset, rangeB.endContainer, rangeB.endOffset),
                endComparison = comparePoints(rangeA.endContainer, rangeA.endOffset, rangeB.startContainer, rangeB.startOffset);

            return touchingIsIntersecting ? startComparison <= 0 && endComparison >= 0 : startComparison < 0 && endComparison > 0;
        }

        function cloneSubtree(iterator) {
            var partiallySelected;
            for (var node, frag = getRangeDocument(iterator.range).createDocumentFragment(), subIterator; node = iterator.next(); ) {
                partiallySelected = iterator.isPartiallySelectedSubtree();
                node = node.cloneNode(!partiallySelected);
                if (partiallySelected) {
                    subIterator = iterator.getSubtreeIterator();
                    node.appendChild(cloneSubtree(subIterator));
                    subIterator.detach();
                }

                if (node.nodeType == 10) { // DocumentType
                    throw new DOMException("HIERARCHY_REQUEST_ERR");
                }
                frag.appendChild(node);
            }
            return frag;
        }

        function iterateSubtree(rangeIterator, func, iteratorState) {
            var it, n;
            iteratorState = iteratorState || { stop: false };
            for (var node, subRangeIterator; node = rangeIterator.next(); ) {
                if (rangeIterator.isPartiallySelectedSubtree()) {
                    if (func(node) === false) {
                        iteratorState.stop = true;
                        return;
                    } else {
                        // The node is partially selected by the Range, so we can use a new RangeIterator on the portion of
                        // the node selected by the Range.
                        subRangeIterator = rangeIterator.getSubtreeIterator();
                        iterateSubtree(subRangeIterator, func, iteratorState);
                        subRangeIterator.detach();
                        if (iteratorState.stop) {
                            return;
                        }
                    }
                } else {
                    // The whole node is selected, so we can use efficient DOM iteration to iterate over the node and its
                    // descendants
                    it = dom.createIterator(node);
                    while ( (n = it.next()) ) {
                        if (func(n) === false) {
                            iteratorState.stop = true;
                            return;
                        }
                    }
                }
            }
        }

        function deleteSubtree(iterator) {
            var subIterator;
            while (iterator.next()) {
                if (iterator.isPartiallySelectedSubtree()) {
                    subIterator = iterator.getSubtreeIterator();
                    deleteSubtree(subIterator);
                    subIterator.detach();
                } else {
                    iterator.remove();
                }
            }
        }

        function extractSubtree(iterator) {
            for (var node, frag = getRangeDocument(iterator.range).createDocumentFragment(), subIterator; node = iterator.next(); ) {

                if (iterator.isPartiallySelectedSubtree()) {
                    node = node.cloneNode(false);
                    subIterator = iterator.getSubtreeIterator();
                    node.appendChild(extractSubtree(subIterator));
                    subIterator.detach();
                } else {
                    iterator.remove();
                }
                if (node.nodeType == 10) { // DocumentType
                    throw new DOMException("HIERARCHY_REQUEST_ERR");
                }
                frag.appendChild(node);
            }
            return frag;
        }

        function getNodesInRange(range, nodeTypes, filter) {
            var filterNodeTypes = !!(nodeTypes && nodeTypes.length), regex;
            var filterExists = !!filter;
            if (filterNodeTypes) {
                regex = new RegExp("^(" + nodeTypes.join("|") + ")$");
            }

            var nodes = [];
            iterateSubtree(new RangeIterator(range, false), function(node) {
                if (filterNodeTypes && !regex.test(node.nodeType)) {
                    return;
                }
                if (filterExists && !filter(node)) {
                    return;
                }
                // Don't include a boundary container if it is a character data node and the range does not contain any
                // of its character data. See issue 190.
                var sc = range.startContainer;
                if (node == sc && isCharacterDataNode(sc) && range.startOffset == sc.length) {
                    return;
                }

                var ec = range.endContainer;
                if (node == ec && isCharacterDataNode(ec) && range.endOffset == 0) {
                    return;
                }

                nodes.push(node);
            });
            return nodes;
        }

        function inspect(range) {
            var name = (typeof range.getName == "undefined") ? "Range" : range.getName();
            return "[" + name + "(" + dom.inspectNode(range.startContainer) + ":" + range.startOffset + ", " +
                    dom.inspectNode(range.endContainer) + ":" + range.endOffset + ")]";
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        // RangeIterator code partially borrows from IERange by Tim Ryan (http://github.com/timcameronryan/IERange)

        function RangeIterator(range, clonePartiallySelectedTextNodes) {
            this.range = range;
            this.clonePartiallySelectedTextNodes = clonePartiallySelectedTextNodes;


            if (!range.collapsed) {
                this.sc = range.startContainer;
                this.so = range.startOffset;
                this.ec = range.endContainer;
                this.eo = range.endOffset;
                var root = range.commonAncestorContainer;

                if (this.sc === this.ec && isCharacterDataNode(this.sc)) {
                    this.isSingleCharacterDataNode = true;
                    this._first = this._last = this._next = this.sc;
                } else {
                    this._first = this._next = (this.sc === root && !isCharacterDataNode(this.sc)) ?
                        this.sc.childNodes[this.so] : getClosestAncestorIn(this.sc, root, true);
                    this._last = (this.ec === root && !isCharacterDataNode(this.ec)) ?
                        this.ec.childNodes[this.eo - 1] : getClosestAncestorIn(this.ec, root, true);
                }
            }
        }

        RangeIterator.prototype = {
            _current: null,
            _next: null,
            _first: null,
            _last: null,
            isSingleCharacterDataNode: false,

            reset: function() {
                this._current = null;
                this._next = this._first;
            },

            hasNext: function() {
                return !!this._next;
            },

            next: function() {
                // Move to next node
                var current = this._current = this._next;
                if (current) {
                    this._next = (current !== this._last) ? current.nextSibling : null;

                    // Check for partially selected text nodes
                    if (isCharacterDataNode(current) && this.clonePartiallySelectedTextNodes) {
                        if (current === this.ec) {
                            (current = current.cloneNode(true)).deleteData(this.eo, current.length - this.eo);
                        }
                        if (this._current === this.sc) {
                            (current = current.cloneNode(true)).deleteData(0, this.so);
                        }
                    }
                }

                return current;
            },

            remove: function() {
                var current = this._current, start, end;

                if (isCharacterDataNode(current) && (current === this.sc || current === this.ec)) {
                    start = (current === this.sc) ? this.so : 0;
                    end = (current === this.ec) ? this.eo : current.length;
                    if (start != end) {
                        current.deleteData(start, end - start);
                    }
                } else {
                    if (current.parentNode) {
                        removeNode(current);
                    } else {
                    }
                }
            },

            // Checks if the current node is partially selected
            isPartiallySelectedSubtree: function() {
                var current = this._current;
                return isNonTextPartiallySelected(current, this.range);
            },

            getSubtreeIterator: function() {
                var subRange;
                if (this.isSingleCharacterDataNode) {
                    subRange = this.range.cloneRange();
                    subRange.collapse(false);
                } else {
                    subRange = new Range(getRangeDocument(this.range));
                    var current = this._current;
                    var startContainer = current, startOffset = 0, endContainer = current, endOffset = getNodeLength(current);

                    if (isOrIsAncestorOf(current, this.sc)) {
                        startContainer = this.sc;
                        startOffset = this.so;
                    }
                    if (isOrIsAncestorOf(current, this.ec)) {
                        endContainer = this.ec;
                        endOffset = this.eo;
                    }

                    updateBoundaries(subRange, startContainer, startOffset, endContainer, endOffset);
                }
                return new RangeIterator(subRange, this.clonePartiallySelectedTextNodes);
            },

            detach: function() {
                this.range = this._current = this._next = this._first = this._last = this.sc = this.so = this.ec = this.eo = null;
            }
        };

        /*----------------------------------------------------------------------------------------------------------------*/

        var beforeAfterNodeTypes = [1, 3, 4, 5, 7, 8, 10];
        var rootContainerNodeTypes = [2, 9, 11];
        var readonlyNodeTypes = [5, 6, 10, 12];
        var insertableNodeTypes = [1, 3, 4, 5, 7, 8, 10, 11];
        var surroundNodeTypes = [1, 3, 4, 5, 7, 8];

        function createAncestorFinder(nodeTypes) {
            return function(node, selfIsAncestor) {
                var t, n = selfIsAncestor ? node : node.parentNode;
                while (n) {
                    t = n.nodeType;
                    if (arrayContains(nodeTypes, t)) {
                        return n;
                    }
                    n = n.parentNode;
                }
                return null;
            };
        }

        var getDocumentOrFragmentContainer = createAncestorFinder( [9, 11] );
        var getReadonlyAncestor = createAncestorFinder(readonlyNodeTypes);
        var getDocTypeNotationEntityAncestor = createAncestorFinder( [6, 10, 12] );
        var getElementAncestor = createAncestorFinder( [1] );

        function assertNoDocTypeNotationEntityAncestor(node, allowSelf) {
            if (getDocTypeNotationEntityAncestor(node, allowSelf)) {
                throw new DOMException("INVALID_NODE_TYPE_ERR");
            }
        }

        function assertValidNodeType(node, invalidTypes) {
            if (!arrayContains(invalidTypes, node.nodeType)) {
                throw new DOMException("INVALID_NODE_TYPE_ERR");
            }
        }

        function assertValidOffset(node, offset) {
            if (offset < 0 || offset > (isCharacterDataNode(node) ? node.length : node.childNodes.length)) {
                throw new DOMException("INDEX_SIZE_ERR");
            }
        }

        function assertSameDocumentOrFragment(node1, node2) {
            if (getDocumentOrFragmentContainer(node1, true) !== getDocumentOrFragmentContainer(node2, true)) {
                throw new DOMException("WRONG_DOCUMENT_ERR");
            }
        }

        function assertNodeNotReadOnly(node) {
            if (getReadonlyAncestor(node, true)) {
                throw new DOMException("NO_MODIFICATION_ALLOWED_ERR");
            }
        }

        function assertNode(node, codeName) {
            if (!node) {
                throw new DOMException(codeName);
            }
        }

        function isValidOffset(node, offset) {
            return offset <= (isCharacterDataNode(node) ? node.length : node.childNodes.length);
        }

        function isRangeValid(range) {
            return (!!range.startContainer && !!range.endContainer &&
                    !(crashyTextNodes && (dom.isBrokenNode(range.startContainer) || dom.isBrokenNode(range.endContainer))) &&
                    getRootContainer(range.startContainer) == getRootContainer(range.endContainer) &&
                    isValidOffset(range.startContainer, range.startOffset) &&
                    isValidOffset(range.endContainer, range.endOffset));
        }

        function assertRangeValid(range) {
            if (!isRangeValid(range)) {
                throw new Error("Range error: Range is not valid. This usually happens after DOM mutation. Range: (" + range.inspect() + ")");
            }
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        // Test the browser's innerHTML support to decide how to implement createContextualFragment
        var styleEl = document.createElement("style");
        var htmlParsingConforms = false;
        try {
            styleEl.innerHTML = "<b>x</b>";
            htmlParsingConforms = (styleEl.firstChild.nodeType == 3); // Pre-Blink Opera incorrectly creates an element node
        } catch (e) {
            // IE 6 and 7 throw
        }

        api.features.htmlParsingConforms = htmlParsingConforms;

        var createContextualFragment = htmlParsingConforms ?

            // Implementation as per HTML parsing spec, trusting in the browser's implementation of innerHTML. See
            // discussion and base code for this implementation at issue 67.
            // Spec: http://html5.org/specs/dom-parsing.html#extensions-to-the-range-interface
            // Thanks to Aleks Williams.
            function(fragmentStr) {
                // "Let node the context object's start's node."
                var node = this.startContainer;
                var doc = getDocument(node);

                // "If the context object's start's node is null, raise an INVALID_STATE_ERR
                // exception and abort these steps."
                if (!node) {
                    throw new DOMException("INVALID_STATE_ERR");
                }

                // "Let element be as follows, depending on node's interface:"
                // Document, Document Fragment: null
                var el = null;

                // "Element: node"
                if (node.nodeType == 1) {
                    el = node;

                // "Text, Comment: node's parentElement"
                } else if (isCharacterDataNode(node)) {
                    el = dom.parentElement(node);
                }

                // "If either element is null or element's ownerDocument is an HTML document
                // and element's local name is "html" and element's namespace is the HTML
                // namespace"
                if (el === null || (
                    el.nodeName == "HTML" &&
                    dom.isHtmlNamespace(getDocument(el).documentElement) &&
                    dom.isHtmlNamespace(el)
                )) {

                // "let element be a new Element with "body" as its local name and the HTML
                // namespace as its namespace.""
                    el = doc.createElement("body");
                } else {
                    el = el.cloneNode(false);
                }

                // "If the node's document is an HTML document: Invoke the HTML fragment parsing algorithm."
                // "If the node's document is an XML document: Invoke the XML fragment parsing algorithm."
                // "In either case, the algorithm must be invoked with fragment as the input
                // and element as the context element."
                el.innerHTML = fragmentStr;

                // "If this raises an exception, then abort these steps. Otherwise, let new
                // children be the nodes returned."

                // "Let fragment be a new DocumentFragment."
                // "Append all new children to fragment."
                // "Return fragment."
                return dom.fragmentFromNodeChildren(el);
            } :

            // In this case, innerHTML cannot be trusted, so fall back to a simpler, non-conformant implementation that
            // previous versions of Rangy used (with the exception of using a body element rather than a div)
            function(fragmentStr) {
                var doc = getRangeDocument(this);
                var el = doc.createElement("body");
                el.innerHTML = fragmentStr;

                return dom.fragmentFromNodeChildren(el);
            };

        function splitRangeBoundaries(range, positionsToPreserve) {
            assertRangeValid(range);

            var sc = range.startContainer, so = range.startOffset, ec = range.endContainer, eo = range.endOffset;
            var startEndSame = (sc === ec);

            if (isCharacterDataNode(ec) && eo > 0 && eo < ec.length) {
                splitDataNode(ec, eo, positionsToPreserve);
            }

            if (isCharacterDataNode(sc) && so > 0 && so < sc.length) {
                sc = splitDataNode(sc, so, positionsToPreserve);
                if (startEndSame) {
                    eo -= so;
                    ec = sc;
                } else if (ec == sc.parentNode && eo >= getNodeIndex(sc)) {
                    eo++;
                }
                so = 0;
            }
            range.setStartAndEnd(sc, so, ec, eo);
        }

        function rangeToHtml(range) {
            assertRangeValid(range);
            var container = range.commonAncestorContainer.parentNode.cloneNode(false);
            container.appendChild( range.cloneContents() );
            return container.innerHTML;
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        var rangeProperties = ["startContainer", "startOffset", "endContainer", "endOffset", "collapsed",
            "commonAncestorContainer"];

        var s2s = 0, s2e = 1, e2e = 2, e2s = 3;
        var n_b = 0, n_a = 1, n_b_a = 2, n_i = 3;

        util.extend(api.rangePrototype, {
            compareBoundaryPoints: function(how, range) {
                assertRangeValid(this);
                assertSameDocumentOrFragment(this.startContainer, range.startContainer);

                var nodeA, offsetA, nodeB, offsetB;
                var prefixA = (how == e2s || how == s2s) ? "start" : "end";
                var prefixB = (how == s2e || how == s2s) ? "start" : "end";
                nodeA = this[prefixA + "Container"];
                offsetA = this[prefixA + "Offset"];
                nodeB = range[prefixB + "Container"];
                offsetB = range[prefixB + "Offset"];
                return comparePoints(nodeA, offsetA, nodeB, offsetB);
            },

            insertNode: function(node) {
                assertRangeValid(this);
                assertValidNodeType(node, insertableNodeTypes);
                assertNodeNotReadOnly(this.startContainer);

                if (isOrIsAncestorOf(node, this.startContainer)) {
                    throw new DOMException("HIERARCHY_REQUEST_ERR");
                }

                // No check for whether the container of the start of the Range is of a type that does not allow
                // children of the type of node: the browser's DOM implementation should do this for us when we attempt
                // to add the node

                var firstNodeInserted = insertNodeAtPosition(node, this.startContainer, this.startOffset);
                this.setStartBefore(firstNodeInserted);
            },

            cloneContents: function() {
                assertRangeValid(this);

                var clone, frag;
                if (this.collapsed) {
                    return getRangeDocument(this).createDocumentFragment();
                } else {
                    if (this.startContainer === this.endContainer && isCharacterDataNode(this.startContainer)) {
                        clone = this.startContainer.cloneNode(true);
                        clone.data = clone.data.slice(this.startOffset, this.endOffset);
                        frag = getRangeDocument(this).createDocumentFragment();
                        frag.appendChild(clone);
                        return frag;
                    } else {
                        var iterator = new RangeIterator(this, true);
                        clone = cloneSubtree(iterator);
                        iterator.detach();
                    }
                    return clone;
                }
            },

            canSurroundContents: function() {
                assertRangeValid(this);
                assertNodeNotReadOnly(this.startContainer);
                assertNodeNotReadOnly(this.endContainer);

                // Check if the contents can be surrounded. Specifically, this means whether the range partially selects
                // no non-text nodes.
                var iterator = new RangeIterator(this, true);
                var boundariesInvalid = (iterator._first && (isNonTextPartiallySelected(iterator._first, this)) ||
                        (iterator._last && isNonTextPartiallySelected(iterator._last, this)));
                iterator.detach();
                return !boundariesInvalid;
            },

            surroundContents: function(node) {
                assertValidNodeType(node, surroundNodeTypes);

                if (!this.canSurroundContents()) {
                    throw new DOMException("INVALID_STATE_ERR");
                }

                // Extract the contents
                var content = this.extractContents();

                // Clear the children of the node
                if (node.hasChildNodes()) {
                    while (node.lastChild) {
                        node.removeChild(node.lastChild);
                    }
                }

                // Insert the new node and add the extracted contents
                insertNodeAtPosition(node, this.startContainer, this.startOffset);
                node.appendChild(content);

                this.selectNode(node);
            },

            cloneRange: function() {
                assertRangeValid(this);
                var range = new Range(getRangeDocument(this));
                var i = rangeProperties.length, prop;
                while (i--) {
                    prop = rangeProperties[i];
                    range[prop] = this[prop];
                }
                return range;
            },

            toString: function() {
                assertRangeValid(this);
                var sc = this.startContainer;
                if (sc === this.endContainer && isCharacterDataNode(sc)) {
                    return (sc.nodeType == 3 || sc.nodeType == 4) ? sc.data.slice(this.startOffset, this.endOffset) : "";
                } else {
                    var textParts = [], iterator = new RangeIterator(this, true);
                    iterateSubtree(iterator, function(node) {
                        // Accept only text or CDATA nodes, not comments
                        if (node.nodeType == 3 || node.nodeType == 4) {
                            textParts.push(node.data);
                        }
                    });
                    iterator.detach();
                    return textParts.join("");
                }
            },

            // The methods below are all non-standard. The following batch were introduced by Mozilla but have since
            // been removed from Mozilla.

            compareNode: function(node) {
                assertRangeValid(this);

                var parent = node.parentNode;
                var nodeIndex = getNodeIndex(node);

                if (!parent) {
                    throw new DOMException("NOT_FOUND_ERR");
                }

                var startComparison = this.comparePoint(parent, nodeIndex),
                    endComparison = this.comparePoint(parent, nodeIndex + 1);

                if (startComparison < 0) { // Node starts before
                    return (endComparison > 0) ? n_b_a : n_b;
                } else {
                    return (endComparison > 0) ? n_a : n_i;
                }
            },

            comparePoint: function(node, offset) {
                assertRangeValid(this);
                assertNode(node, "HIERARCHY_REQUEST_ERR");
                assertSameDocumentOrFragment(node, this.startContainer);

                if (comparePoints(node, offset, this.startContainer, this.startOffset) < 0) {
                    return -1;
                } else if (comparePoints(node, offset, this.endContainer, this.endOffset) > 0) {
                    return 1;
                }
                return 0;
            },

            createContextualFragment: createContextualFragment,

            toHtml: function() {
                return rangeToHtml(this);
            },

            // touchingIsIntersecting determines whether this method considers a node that borders a range intersects
            // with it (as in WebKit) or not (as in Gecko pre-1.9, and the default)
            intersectsNode: function(node, touchingIsIntersecting) {
                assertRangeValid(this);
                if (getRootContainer(node) != getRangeRoot(this)) {
                    return false;
                }

                var parent = node.parentNode, offset = getNodeIndex(node);
                if (!parent) {
                    return true;
                }

                var startComparison = comparePoints(parent, offset, this.endContainer, this.endOffset),
                    endComparison = comparePoints(parent, offset + 1, this.startContainer, this.startOffset);

                return touchingIsIntersecting ? startComparison <= 0 && endComparison >= 0 : startComparison < 0 && endComparison > 0;
            },

            isPointInRange: function(node, offset) {
                assertRangeValid(this);
                assertNode(node, "HIERARCHY_REQUEST_ERR");
                assertSameDocumentOrFragment(node, this.startContainer);

                return (comparePoints(node, offset, this.startContainer, this.startOffset) >= 0) &&
                       (comparePoints(node, offset, this.endContainer, this.endOffset) <= 0);
            },

            // The methods below are non-standard and invented by me.

            // Sharing a boundary start-to-end or end-to-start does not count as intersection.
            intersectsRange: function(range) {
                return rangesIntersect(this, range, false);
            },

            // Sharing a boundary start-to-end or end-to-start does count as intersection.
            intersectsOrTouchesRange: function(range) {
                return rangesIntersect(this, range, true);
            },

            intersection: function(range) {
                if (this.intersectsRange(range)) {
                    var startComparison = comparePoints(this.startContainer, this.startOffset, range.startContainer, range.startOffset),
                        endComparison = comparePoints(this.endContainer, this.endOffset, range.endContainer, range.endOffset);

                    var intersectionRange = this.cloneRange();
                    if (startComparison == -1) {
                        intersectionRange.setStart(range.startContainer, range.startOffset);
                    }
                    if (endComparison == 1) {
                        intersectionRange.setEnd(range.endContainer, range.endOffset);
                    }
                    return intersectionRange;
                }
                return null;
            },

            union: function(range) {
                if (this.intersectsOrTouchesRange(range)) {
                    var unionRange = this.cloneRange();
                    if (comparePoints(range.startContainer, range.startOffset, this.startContainer, this.startOffset) == -1) {
                        unionRange.setStart(range.startContainer, range.startOffset);
                    }
                    if (comparePoints(range.endContainer, range.endOffset, this.endContainer, this.endOffset) == 1) {
                        unionRange.setEnd(range.endContainer, range.endOffset);
                    }
                    return unionRange;
                } else {
                    throw new DOMException("Ranges do not intersect");
                }
            },

            containsNode: function(node, allowPartial) {
                if (allowPartial) {
                    return this.intersectsNode(node, false);
                } else {
                    return this.compareNode(node) == n_i;
                }
            },

            containsNodeContents: function(node) {
                return this.comparePoint(node, 0) >= 0 && this.comparePoint(node, getNodeLength(node)) <= 0;
            },

            containsRange: function(range) {
                var intersection = this.intersection(range);
                return intersection !== null && range.equals(intersection);
            },

            containsNodeText: function(node) {
                var nodeRange = this.cloneRange();
                nodeRange.selectNode(node);
                var textNodes = nodeRange.getNodes([3]);
                if (textNodes.length > 0) {
                    nodeRange.setStart(textNodes[0], 0);
                    var lastTextNode = textNodes.pop();
                    nodeRange.setEnd(lastTextNode, lastTextNode.length);
                    return this.containsRange(nodeRange);
                } else {
                    return this.containsNodeContents(node);
                }
            },

            getNodes: function(nodeTypes, filter) {
                assertRangeValid(this);
                return getNodesInRange(this, nodeTypes, filter);
            },

            getDocument: function() {
                return getRangeDocument(this);
            },

            collapseBefore: function(node) {
                this.setEndBefore(node);
                this.collapse(false);
            },

            collapseAfter: function(node) {
                this.setStartAfter(node);
                this.collapse(true);
            },

            getBookmark: function(containerNode) {
                var doc = getRangeDocument(this);
                var preSelectionRange = api.createRange(doc);
                containerNode = containerNode || dom.getBody(doc);
                preSelectionRange.selectNodeContents(containerNode);
                var range = this.intersection(preSelectionRange);
                var start = 0, end = 0;
                if (range) {
                    preSelectionRange.setEnd(range.startContainer, range.startOffset);
                    start = preSelectionRange.toString().length;
                    end = start + range.toString().length;
                }

                return {
                    start: start,
                    end: end,
                    containerNode: containerNode
                };
            },

            moveToBookmark: function(bookmark) {
                var containerNode = bookmark.containerNode;
                var charIndex = 0;
                this.setStart(containerNode, 0);
                this.collapse(true);
                var nodeStack = [containerNode], node, foundStart = false, stop = false;
                var nextCharIndex, i, childNodes;

                while (!stop && (node = nodeStack.pop())) {
                    if (node.nodeType == 3) {
                        nextCharIndex = charIndex + node.length;
                        if (!foundStart && bookmark.start >= charIndex && bookmark.start <= nextCharIndex) {
                            this.setStart(node, bookmark.start - charIndex);
                            foundStart = true;
                        }
                        if (foundStart && bookmark.end >= charIndex && bookmark.end <= nextCharIndex) {
                            this.setEnd(node, bookmark.end - charIndex);
                            stop = true;
                        }
                        charIndex = nextCharIndex;
                    } else {
                        childNodes = node.childNodes;
                        i = childNodes.length;
                        while (i--) {
                            nodeStack.push(childNodes[i]);
                        }
                    }
                }
            },

            getName: function() {
                return "DomRange";
            },

            equals: function(range) {
                return Range.rangesEqual(this, range);
            },

            isValid: function() {
                return isRangeValid(this);
            },

            inspect: function() {
                return inspect(this);
            },

            detach: function() {
                // In DOM4, detach() is now a no-op.
            }
        });

        function copyComparisonConstantsToObject(obj) {
            obj.START_TO_START = s2s;
            obj.START_TO_END = s2e;
            obj.END_TO_END = e2e;
            obj.END_TO_START = e2s;

            obj.NODE_BEFORE = n_b;
            obj.NODE_AFTER = n_a;
            obj.NODE_BEFORE_AND_AFTER = n_b_a;
            obj.NODE_INSIDE = n_i;
        }

        function copyComparisonConstants(constructor) {
            copyComparisonConstantsToObject(constructor);
            copyComparisonConstantsToObject(constructor.prototype);
        }

        function createRangeContentRemover(remover, boundaryUpdater) {
            return function() {
                assertRangeValid(this);

                var sc = this.startContainer, so = this.startOffset, root = this.commonAncestorContainer;

                var iterator = new RangeIterator(this, true);

                // Work out where to position the range after content removal
                var node, boundary;
                if (sc !== root) {
                    node = getClosestAncestorIn(sc, root, true);
                    boundary = getBoundaryAfterNode(node);
                    sc = boundary.node;
                    so = boundary.offset;
                }

                // Check none of the range is read-only
                iterateSubtree(iterator, assertNodeNotReadOnly);

                iterator.reset();

                // Remove the content
                var returnValue = remover(iterator);
                iterator.detach();

                // Move to the new position
                boundaryUpdater(this, sc, so, sc, so);

                return returnValue;
            };
        }

        function createPrototypeRange(constructor, boundaryUpdater) {
            function createBeforeAfterNodeSetter(isBefore, isStart) {
                return function(node) {
                    assertValidNodeType(node, beforeAfterNodeTypes);
                    assertValidNodeType(getRootContainer(node), rootContainerNodeTypes);

                    var boundary = (isBefore ? getBoundaryBeforeNode : getBoundaryAfterNode)(node);
                    (isStart ? setRangeStart : setRangeEnd)(this, boundary.node, boundary.offset);
                };
            }

            function setRangeStart(range, node, offset) {
                var ec = range.endContainer, eo = range.endOffset;
                if (node !== range.startContainer || offset !== range.startOffset) {
                    // Check the root containers of the range and the new boundary, and also check whether the new boundary
                    // is after the current end. In either case, collapse the range to the new position
                    if (getRootContainer(node) != getRootContainer(ec) || comparePoints(node, offset, ec, eo) == 1) {
                        ec = node;
                        eo = offset;
                    }
                    boundaryUpdater(range, node, offset, ec, eo);
                }
            }

            function setRangeEnd(range, node, offset) {
                var sc = range.startContainer, so = range.startOffset;
                if (node !== range.endContainer || offset !== range.endOffset) {
                    // Check the root containers of the range and the new boundary, and also check whether the new boundary
                    // is after the current end. In either case, collapse the range to the new position
                    if (getRootContainer(node) != getRootContainer(sc) || comparePoints(node, offset, sc, so) == -1) {
                        sc = node;
                        so = offset;
                    }
                    boundaryUpdater(range, sc, so, node, offset);
                }
            }

            // Set up inheritance
            var F = function() {};
            F.prototype = api.rangePrototype;
            constructor.prototype = new F();

            util.extend(constructor.prototype, {
                setStart: function(node, offset) {
                    assertNoDocTypeNotationEntityAncestor(node, true);
                    assertValidOffset(node, offset);

                    setRangeStart(this, node, offset);
                },

                setEnd: function(node, offset) {
                    assertNoDocTypeNotationEntityAncestor(node, true);
                    assertValidOffset(node, offset);

                    setRangeEnd(this, node, offset);
                },

                /**
                 * Convenience method to set a range's start and end boundaries. Overloaded as follows:
                 * - Two parameters (node, offset) creates a collapsed range at that position
                 * - Three parameters (node, startOffset, endOffset) creates a range contained with node starting at
                 *   startOffset and ending at endOffset
                 * - Four parameters (startNode, startOffset, endNode, endOffset) creates a range starting at startOffset in
                 *   startNode and ending at endOffset in endNode
                 */
                setStartAndEnd: function() {
                    var args = arguments;
                    var sc = args[0], so = args[1], ec = sc, eo = so;

                    switch (args.length) {
                        case 3:
                            eo = args[2];
                            break;
                        case 4:
                            ec = args[2];
                            eo = args[3];
                            break;
                    }

                    assertNoDocTypeNotationEntityAncestor(sc, true);
                    assertValidOffset(sc, so);

                    assertNoDocTypeNotationEntityAncestor(ec, true);
                    assertValidOffset(ec, eo);

                    boundaryUpdater(this, sc, so, ec, eo);
                },

                setBoundary: function(node, offset, isStart) {
                    this["set" + (isStart ? "Start" : "End")](node, offset);
                },

                setStartBefore: createBeforeAfterNodeSetter(true, true),
                setStartAfter: createBeforeAfterNodeSetter(false, true),
                setEndBefore: createBeforeAfterNodeSetter(true, false),
                setEndAfter: createBeforeAfterNodeSetter(false, false),

                collapse: function(isStart) {
                    assertRangeValid(this);
                    if (isStart) {
                        boundaryUpdater(this, this.startContainer, this.startOffset, this.startContainer, this.startOffset);
                    } else {
                        boundaryUpdater(this, this.endContainer, this.endOffset, this.endContainer, this.endOffset);
                    }
                },

                selectNodeContents: function(node) {
                    assertNoDocTypeNotationEntityAncestor(node, true);

                    boundaryUpdater(this, node, 0, node, getNodeLength(node));
                },

                selectNode: function(node) {
                    assertNoDocTypeNotationEntityAncestor(node, false);
                    assertValidNodeType(node, beforeAfterNodeTypes);

                    var start = getBoundaryBeforeNode(node), end = getBoundaryAfterNode(node);
                    boundaryUpdater(this, start.node, start.offset, end.node, end.offset);
                },

                extractContents: createRangeContentRemover(extractSubtree, boundaryUpdater),

                deleteContents: createRangeContentRemover(deleteSubtree, boundaryUpdater),

                canSurroundContents: function() {
                    assertRangeValid(this);
                    assertNodeNotReadOnly(this.startContainer);
                    assertNodeNotReadOnly(this.endContainer);

                    // Check if the contents can be surrounded. Specifically, this means whether the range partially selects
                    // no non-text nodes.
                    var iterator = new RangeIterator(this, true);
                    var boundariesInvalid = (iterator._first && isNonTextPartiallySelected(iterator._first, this) ||
                            (iterator._last && isNonTextPartiallySelected(iterator._last, this)));
                    iterator.detach();
                    return !boundariesInvalid;
                },

                splitBoundaries: function() {
                    splitRangeBoundaries(this);
                },

                splitBoundariesPreservingPositions: function(positionsToPreserve) {
                    splitRangeBoundaries(this, positionsToPreserve);
                },

                normalizeBoundaries: function() {
                    assertRangeValid(this);

                    var sc = this.startContainer, so = this.startOffset, ec = this.endContainer, eo = this.endOffset;

                    var mergeForward = function(node) {
                        var sibling = node.nextSibling;
                        if (sibling && sibling.nodeType == node.nodeType) {
                            ec = node;
                            eo = node.length;
                            node.appendData(sibling.data);
                            removeNode(sibling);
                        }
                    };

                    var mergeBackward = function(node) {
                        var sibling = node.previousSibling;
                        if (sibling && sibling.nodeType == node.nodeType) {
                            sc = node;
                            var nodeLength = node.length;
                            so = sibling.length;
                            node.insertData(0, sibling.data);
                            removeNode(sibling);
                            if (sc == ec) {
                                eo += so;
                                ec = sc;
                            } else if (ec == node.parentNode) {
                                var nodeIndex = getNodeIndex(node);
                                if (eo == nodeIndex) {
                                    ec = node;
                                    eo = nodeLength;
                                } else if (eo > nodeIndex) {
                                    eo--;
                                }
                            }
                        }
                    };

                    var normalizeStart = true;
                    var sibling;

                    if (isCharacterDataNode(ec)) {
                        if (eo == ec.length) {
                            mergeForward(ec);
                        } else if (eo == 0) {
                            sibling = ec.previousSibling;
                            if (sibling && sibling.nodeType == ec.nodeType) {
                                eo = sibling.length;
                                if (sc == ec) {
                                    normalizeStart = false;
                                }
                                sibling.appendData(ec.data);
                                removeNode(ec);
                                ec = sibling;
                            }
                        }
                    } else {
                        if (eo > 0) {
                            var endNode = ec.childNodes[eo - 1];
                            if (endNode && isCharacterDataNode(endNode)) {
                                mergeForward(endNode);
                            }
                        }
                        normalizeStart = !this.collapsed;
                    }

                    if (normalizeStart) {
                        if (isCharacterDataNode(sc)) {
                            if (so == 0) {
                                mergeBackward(sc);
                            } else if (so == sc.length) {
                                sibling = sc.nextSibling;
                                if (sibling && sibling.nodeType == sc.nodeType) {
                                    if (ec == sibling) {
                                        ec = sc;
                                        eo += sc.length;
                                    }
                                    sc.appendData(sibling.data);
                                    removeNode(sibling);
                                }
                            }
                        } else {
                            if (so < sc.childNodes.length) {
                                var startNode = sc.childNodes[so];
                                if (startNode && isCharacterDataNode(startNode)) {
                                    mergeBackward(startNode);
                                }
                            }
                        }
                    } else {
                        sc = ec;
                        so = eo;
                    }

                    boundaryUpdater(this, sc, so, ec, eo);
                },

                collapseToPoint: function(node, offset) {
                    assertNoDocTypeNotationEntityAncestor(node, true);
                    assertValidOffset(node, offset);
                    this.setStartAndEnd(node, offset);
                },

                parentElement: function() {
                    assertRangeValid(this);
                    var parentNode = this.commonAncestorContainer;
                    return parentNode ? getElementAncestor(this.commonAncestorContainer, true) : null;
                }
            });

            copyComparisonConstants(constructor);
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        // Updates commonAncestorContainer and collapsed after boundary change
        function updateCollapsedAndCommonAncestor(range) {
            range.collapsed = (range.startContainer === range.endContainer && range.startOffset === range.endOffset);
            range.commonAncestorContainer = range.collapsed ?
                range.startContainer : dom.getCommonAncestor(range.startContainer, range.endContainer);
        }

        function updateBoundaries(range, startContainer, startOffset, endContainer, endOffset) {
            range.startContainer = startContainer;
            range.startOffset = startOffset;
            range.endContainer = endContainer;
            range.endOffset = endOffset;
            range.document = dom.getDocument(startContainer);
            updateCollapsedAndCommonAncestor(range);
        }

        function Range(doc) {
            updateBoundaries(this, doc, 0, doc, 0);
        }

        createPrototypeRange(Range, updateBoundaries);

        util.extend(Range, {
            rangeProperties: rangeProperties,
            RangeIterator: RangeIterator,
            copyComparisonConstants: copyComparisonConstants,
            createPrototypeRange: createPrototypeRange,
            inspect: inspect,
            toHtml: rangeToHtml,
            getRangeDocument: getRangeDocument,
            rangesEqual: function(r1, r2) {
                return r1.startContainer === r2.startContainer &&
                    r1.startOffset === r2.startOffset &&
                    r1.endContainer === r2.endContainer &&
                    r1.endOffset === r2.endOffset;
            }
        });

        api.DomRange = Range;
    });

    /*----------------------------------------------------------------------------------------------------------------*/

    // Wrappers for the browser's native DOM Range and/or TextRange implementation
    api.createCoreModule("WrappedRange", ["DomRange"], function(api, module) {
        var WrappedRange, WrappedTextRange;
        var dom = api.dom;
        var util = api.util;
        var DomPosition = dom.DomPosition;
        var DomRange = api.DomRange;
        var getBody = dom.getBody;
        var getContentDocument = dom.getContentDocument;
        var isCharacterDataNode = dom.isCharacterDataNode;


        /*----------------------------------------------------------------------------------------------------------------*/

        if (api.features.implementsDomRange) {
            // This is a wrapper around the browser's native DOM Range. It has two aims:
            // - Provide workarounds for specific browser bugs
            // - provide convenient extensions, which are inherited from Rangy's DomRange

            (function() {
                var rangeProto;
                var rangeProperties = DomRange.rangeProperties;

                function updateRangeProperties(range) {
                    var i = rangeProperties.length, prop;
                    while (i--) {
                        prop = rangeProperties[i];
                        range[prop] = range.nativeRange[prop];
                    }
                    // Fix for broken collapsed property in IE 9.
                    range.collapsed = (range.startContainer === range.endContainer && range.startOffset === range.endOffset);
                }

                function updateNativeRange(range, startContainer, startOffset, endContainer, endOffset) {
                    var startMoved = (range.startContainer !== startContainer || range.startOffset != startOffset);
                    var endMoved = (range.endContainer !== endContainer || range.endOffset != endOffset);
                    var nativeRangeDifferent = !range.equals(range.nativeRange);

                    // Always set both boundaries for the benefit of IE9 (see issue 35)
                    if (startMoved || endMoved || nativeRangeDifferent) {
                        range.setEnd(endContainer, endOffset);
                        range.setStart(startContainer, startOffset);
                    }
                }

                var createBeforeAfterNodeSetter;

                WrappedRange = function(range) {
                    if (!range) {
                        throw module.createError("WrappedRange: Range must be specified");
                    }
                    this.nativeRange = range;
                    updateRangeProperties(this);
                };

                DomRange.createPrototypeRange(WrappedRange, updateNativeRange);

                rangeProto = WrappedRange.prototype;

                rangeProto.selectNode = function(node) {
                    this.nativeRange.selectNode(node);
                    updateRangeProperties(this);
                };

                rangeProto.cloneContents = function() {
                    return this.nativeRange.cloneContents();
                };

                // Due to a long-standing Firefox bug that I have not been able to find a reliable way to detect,
                // insertNode() is never delegated to the native range.

                rangeProto.surroundContents = function(node) {
                    this.nativeRange.surroundContents(node);
                    updateRangeProperties(this);
                };

                rangeProto.collapse = function(isStart) {
                    this.nativeRange.collapse(isStart);
                    updateRangeProperties(this);
                };

                rangeProto.cloneRange = function() {
                    return new WrappedRange(this.nativeRange.cloneRange());
                };

                rangeProto.refresh = function() {
                    updateRangeProperties(this);
                };

                rangeProto.toString = function() {
                    return this.nativeRange.toString();
                };

                // Create test range and node for feature detection

                var testTextNode = document.createTextNode("test");
                getBody(document).appendChild(testTextNode);
                var range = document.createRange();

                /*--------------------------------------------------------------------------------------------------------*/

                // Test for Firefox 2 bug that prevents moving the start of a Range to a point after its current end and
                // correct for it

                range.setStart(testTextNode, 0);
                range.setEnd(testTextNode, 0);

                try {
                    range.setStart(testTextNode, 1);

                    rangeProto.setStart = function(node, offset) {
                        this.nativeRange.setStart(node, offset);
                        updateRangeProperties(this);
                    };

                    rangeProto.setEnd = function(node, offset) {
                        this.nativeRange.setEnd(node, offset);
                        updateRangeProperties(this);
                    };

                    createBeforeAfterNodeSetter = function(name) {
                        return function(node) {
                            this.nativeRange[name](node);
                            updateRangeProperties(this);
                        };
                    };

                } catch(ex) {

                    rangeProto.setStart = function(node, offset) {
                        try {
                            this.nativeRange.setStart(node, offset);
                        } catch (ex) {
                            this.nativeRange.setEnd(node, offset);
                            this.nativeRange.setStart(node, offset);
                        }
                        updateRangeProperties(this);
                    };

                    rangeProto.setEnd = function(node, offset) {
                        try {
                            this.nativeRange.setEnd(node, offset);
                        } catch (ex) {
                            this.nativeRange.setStart(node, offset);
                            this.nativeRange.setEnd(node, offset);
                        }
                        updateRangeProperties(this);
                    };

                    createBeforeAfterNodeSetter = function(name, oppositeName) {
                        return function(node) {
                            try {
                                this.nativeRange[name](node);
                            } catch (ex) {
                                this.nativeRange[oppositeName](node);
                                this.nativeRange[name](node);
                            }
                            updateRangeProperties(this);
                        };
                    };
                }

                rangeProto.setStartBefore = createBeforeAfterNodeSetter("setStartBefore", "setEndBefore");
                rangeProto.setStartAfter = createBeforeAfterNodeSetter("setStartAfter", "setEndAfter");
                rangeProto.setEndBefore = createBeforeAfterNodeSetter("setEndBefore", "setStartBefore");
                rangeProto.setEndAfter = createBeforeAfterNodeSetter("setEndAfter", "setStartAfter");

                /*--------------------------------------------------------------------------------------------------------*/

                // Always use DOM4-compliant selectNodeContents implementation: it's simpler and less code than testing
                // whether the native implementation can be trusted
                rangeProto.selectNodeContents = function(node) {
                    this.setStartAndEnd(node, 0, dom.getNodeLength(node));
                };

                /*--------------------------------------------------------------------------------------------------------*/

                // Test for and correct WebKit bug that has the behaviour of compareBoundaryPoints round the wrong way for
                // constants START_TO_END and END_TO_START: https://bugs.webkit.org/show_bug.cgi?id=20738

                range.selectNodeContents(testTextNode);
                range.setEnd(testTextNode, 3);

                var range2 = document.createRange();
                range2.selectNodeContents(testTextNode);
                range2.setEnd(testTextNode, 4);
                range2.setStart(testTextNode, 2);

                if (range.compareBoundaryPoints(range.START_TO_END, range2) == -1 &&
                        range.compareBoundaryPoints(range.END_TO_START, range2) == 1) {
                    // This is the wrong way round, so correct for it

                    rangeProto.compareBoundaryPoints = function(type, range) {
                        range = range.nativeRange || range;
                        if (type == range.START_TO_END) {
                            type = range.END_TO_START;
                        } else if (type == range.END_TO_START) {
                            type = range.START_TO_END;
                        }
                        return this.nativeRange.compareBoundaryPoints(type, range);
                    };
                } else {
                    rangeProto.compareBoundaryPoints = function(type, range) {
                        return this.nativeRange.compareBoundaryPoints(type, range.nativeRange || range);
                    };
                }

                /*--------------------------------------------------------------------------------------------------------*/

                // Test for IE deleteContents() and extractContents() bug and correct it. See issue 107.

                var el = document.createElement("div");
                el.innerHTML = "123";
                var textNode = el.firstChild;
                var body = getBody(document);
                body.appendChild(el);

                range.setStart(textNode, 1);
                range.setEnd(textNode, 2);
                range.deleteContents();

                if (textNode.data == "13") {
                    // Behaviour is correct per DOM4 Range so wrap the browser's implementation of deleteContents() and
                    // extractContents()
                    rangeProto.deleteContents = function() {
                        this.nativeRange.deleteContents();
                        updateRangeProperties(this);
                    };

                    rangeProto.extractContents = function() {
                        var frag = this.nativeRange.extractContents();
                        updateRangeProperties(this);
                        return frag;
                    };
                } else {
                }

                body.removeChild(el);
                body = null;

                /*--------------------------------------------------------------------------------------------------------*/

                // Test for existence of createContextualFragment and delegate to it if it exists
                if (util.isHostMethod(range, "createContextualFragment")) {
                    rangeProto.createContextualFragment = function(fragmentStr) {
                        return this.nativeRange.createContextualFragment(fragmentStr);
                    };
                }

                /*--------------------------------------------------------------------------------------------------------*/

                // Clean up
                getBody(document).removeChild(testTextNode);

                rangeProto.getName = function() {
                    return "WrappedRange";
                };

                api.WrappedRange = WrappedRange;

                api.createNativeRange = function(doc) {
                    doc = getContentDocument(doc, module, "createNativeRange");
                    return doc.createRange();
                };
            })();
        }

        if (api.features.implementsTextRange) {
            /*
            This is a workaround for a bug where IE returns the wrong container element from the TextRange's parentElement()
            method. For example, in the following (where pipes denote the selection boundaries):

            <ul id="ul"><li id="a">| a </li><li id="b"> b |</li></ul>

            var range = document.selection.createRange();
            alert(range.parentElement().id); // Should alert "ul" but alerts "b"

            This method returns the common ancestor node of the following:
            - the parentElement() of the textRange
            - the parentElement() of the textRange after calling collapse(true)
            - the parentElement() of the textRange after calling collapse(false)
            */
            var getTextRangeContainerElement = function(textRange) {
                var parentEl = textRange.parentElement();
                var range = textRange.duplicate();
                range.collapse(true);
                var startEl = range.parentElement();
                range = textRange.duplicate();
                range.collapse(false);
                var endEl = range.parentElement();
                var startEndContainer = (startEl == endEl) ? startEl : dom.getCommonAncestor(startEl, endEl);

                return startEndContainer == parentEl ? startEndContainer : dom.getCommonAncestor(parentEl, startEndContainer);
            };

            var textRangeIsCollapsed = function(textRange) {
                return textRange.compareEndPoints("StartToEnd", textRange) == 0;
            };

            // Gets the boundary of a TextRange expressed as a node and an offset within that node. This function started
            // out as an improved version of code found in Tim Cameron Ryan's IERange (http://code.google.com/p/ierange/)
            // but has grown, fixing problems with line breaks in preformatted text, adding workaround for IE TextRange
            // bugs, handling for inputs and images, plus optimizations.
            var getTextRangeBoundaryPosition = function(textRange, wholeRangeContainerElement, isStart, isCollapsed, startInfo) {
                var workingRange = textRange.duplicate();
                workingRange.collapse(isStart);
                var containerElement = workingRange.parentElement();

                // Sometimes collapsing a TextRange that's at the start of a text node can move it into the previous node, so
                // check for that
                if (!dom.isOrIsAncestorOf(wholeRangeContainerElement, containerElement)) {
                    containerElement = wholeRangeContainerElement;
                }


                // Deal with nodes that cannot "contain rich HTML markup". In practice, this means form inputs, images and
                // similar. See http://msdn.microsoft.com/en-us/library/aa703950%28VS.85%29.aspx
                if (!containerElement.canHaveHTML) {
                    var pos = new DomPosition(containerElement.parentNode, dom.getNodeIndex(containerElement));
                    return {
                        boundaryPosition: pos,
                        nodeInfo: {
                            nodeIndex: pos.offset,
                            containerElement: pos.node
                        }
                    };
                }

                var workingNode = dom.getDocument(containerElement).createElement("span");

                // Workaround for HTML5 Shiv's insane violation of document.createElement(). See Rangy issue 104 and HTML5
                // Shiv issue 64: https://github.com/aFarkas/html5shiv/issues/64
                if (workingNode.parentNode) {
                    dom.removeNode(workingNode);
                }

                var comparison, workingComparisonType = isStart ? "StartToStart" : "StartToEnd";
                var previousNode, nextNode, boundaryPosition, boundaryNode;
                var start = (startInfo && startInfo.containerElement == containerElement) ? startInfo.nodeIndex : 0;
                var childNodeCount = containerElement.childNodes.length;
                var end = childNodeCount;

                // Check end first. Code within the loop assumes that the endth child node of the container is definitely
                // after the range boundary.
                var nodeIndex = end;

                while (true) {
                    if (nodeIndex == childNodeCount) {
                        containerElement.appendChild(workingNode);
                    } else {
                        containerElement.insertBefore(workingNode, containerElement.childNodes[nodeIndex]);
                    }
                    workingRange.moveToElementText(workingNode);
                    comparison = workingRange.compareEndPoints(workingComparisonType, textRange);
                    if (comparison == 0 || start == end) {
                        break;
                    } else if (comparison == -1) {
                        if (end == start + 1) {
                            // We know the endth child node is after the range boundary, so we must be done.
                            break;
                        } else {
                            start = nodeIndex;
                        }
                    } else {
                        end = (end == start + 1) ? start : nodeIndex;
                    }
                    nodeIndex = Math.floor((start + end) / 2);
                    containerElement.removeChild(workingNode);
                }


                // We've now reached or gone past the boundary of the text range we're interested in
                // so have identified the node we want
                boundaryNode = workingNode.nextSibling;

                if (comparison == -1 && boundaryNode && isCharacterDataNode(boundaryNode)) {
                    // This is a character data node (text, comment, cdata). The working range is collapsed at the start of
                    // the node containing the text range's boundary, so we move the end of the working range to the
                    // boundary point and measure the length of its text to get the boundary's offset within the node.
                    workingRange.setEndPoint(isStart ? "EndToStart" : "EndToEnd", textRange);

                    var offset;

                    if (/[\r\n]/.test(boundaryNode.data)) {
                        /*
                        For the particular case of a boundary within a text node containing rendered line breaks (within a
                        <pre> element, for example), we need a slightly complicated approach to get the boundary's offset in
                        IE. The facts:

                        - Each line break is represented as \r in the text node's data/nodeValue properties
                        - Each line break is represented as \r\n in the TextRange's 'text' property
                        - The 'text' property of the TextRange does not contain trailing line breaks

                        To get round the problem presented by the final fact above, we can use the fact that TextRange's
                        moveStart() and moveEnd() methods return the actual number of characters moved, which is not
                        necessarily the same as the number of characters it was instructed to move. The simplest approach is
                        to use this to store the characters moved when moving both the start and end of the range to the
                        start of the document body and subtracting the start offset from the end offset (the
                        "move-negative-gazillion" method). However, this is extremely slow when the document is large and
                        the range is near the end of it. Clearly doing the mirror image (i.e. moving the range boundaries to
                        the end of the document) has the same problem.

                        Another approach that works is to use moveStart() to move the start boundary of the range up to the
                        end boundary one character at a time and incrementing a counter with the value returned by the
                        moveStart() call. However, the check for whether the start boundary has reached the end boundary is
                        expensive, so this method is slow (although unlike "move-negative-gazillion" is largely unaffected
                        by the location of the range within the document).

                        The approach used below is a hybrid of the two methods above. It uses the fact that a string
                        containing the TextRange's 'text' property with each \r\n converted to a single \r character cannot
                        be longer than the text of the TextRange, so the start of the range is moved that length initially
                        and then a character at a time to make up for any trailing line breaks not contained in the 'text'
                        property. This has good performance in most situations compared to the previous two methods.
                        */
                        var tempRange = workingRange.duplicate();
                        var rangeLength = tempRange.text.replace(/\r\n/g, "\r").length;

                        offset = tempRange.moveStart("character", rangeLength);
                        while ( (comparison = tempRange.compareEndPoints("StartToEnd", tempRange)) == -1) {
                            offset++;
                            tempRange.moveStart("character", 1);
                        }
                    } else {
                        offset = workingRange.text.length;
                    }
                    boundaryPosition = new DomPosition(boundaryNode, offset);
                } else {

                    // If the boundary immediately follows a character data node and this is the end boundary, we should favour
                    // a position within that, and likewise for a start boundary preceding a character data node
                    previousNode = (isCollapsed || !isStart) && workingNode.previousSibling;
                    nextNode = (isCollapsed || isStart) && workingNode.nextSibling;
                    if (nextNode && isCharacterDataNode(nextNode)) {
                        boundaryPosition = new DomPosition(nextNode, 0);
                    } else if (previousNode && isCharacterDataNode(previousNode)) {
                        boundaryPosition = new DomPosition(previousNode, previousNode.data.length);
                    } else {
                        boundaryPosition = new DomPosition(containerElement, dom.getNodeIndex(workingNode));
                    }
                }

                // Clean up
                dom.removeNode(workingNode);

                return {
                    boundaryPosition: boundaryPosition,
                    nodeInfo: {
                        nodeIndex: nodeIndex,
                        containerElement: containerElement
                    }
                };
            };

            // Returns a TextRange representing the boundary of a TextRange expressed as a node and an offset within that
            // node. This function started out as an optimized version of code found in Tim Cameron Ryan's IERange
            // (http://code.google.com/p/ierange/)
            var createBoundaryTextRange = function(boundaryPosition, isStart) {
                var boundaryNode, boundaryParent, boundaryOffset = boundaryPosition.offset;
                var doc = dom.getDocument(boundaryPosition.node);
                var workingNode, childNodes, workingRange = getBody(doc).createTextRange();
                var nodeIsDataNode = isCharacterDataNode(boundaryPosition.node);

                if (nodeIsDataNode) {
                    boundaryNode = boundaryPosition.node;
                    boundaryParent = boundaryNode.parentNode;
                } else {
                    childNodes = boundaryPosition.node.childNodes;
                    boundaryNode = (boundaryOffset < childNodes.length) ? childNodes[boundaryOffset] : null;
                    boundaryParent = boundaryPosition.node;
                }

                // Position the range immediately before the node containing the boundary
                workingNode = doc.createElement("span");

                // Making the working element non-empty element persuades IE to consider the TextRange boundary to be within
                // the element rather than immediately before or after it
                workingNode.innerHTML = "&#feff;";

                // insertBefore is supposed to work like appendChild if the second parameter is null. However, a bug report
                // for IERange suggests that it can crash the browser: http://code.google.com/p/ierange/issues/detail?id=12
                if (boundaryNode) {
                    boundaryParent.insertBefore(workingNode, boundaryNode);
                } else {
                    boundaryParent.appendChild(workingNode);
                }

                workingRange.moveToElementText(workingNode);
                workingRange.collapse(!isStart);

                // Clean up
                boundaryParent.removeChild(workingNode);

                // Move the working range to the text offset, if required
                if (nodeIsDataNode) {
                    workingRange[isStart ? "moveStart" : "moveEnd"]("character", boundaryOffset);
                }

                return workingRange;
            };

            /*------------------------------------------------------------------------------------------------------------*/

            // This is a wrapper around a TextRange, providing full DOM Range functionality using rangy's DomRange as a
            // prototype

            WrappedTextRange = function(textRange) {
                this.textRange = textRange;
                this.refresh();
            };

            WrappedTextRange.prototype = new DomRange(document);

            WrappedTextRange.prototype.refresh = function() {
                var start, end, startBoundary;

                // TextRange's parentElement() method cannot be trusted. getTextRangeContainerElement() works around that.
                var rangeContainerElement = getTextRangeContainerElement(this.textRange);

                if (textRangeIsCollapsed(this.textRange)) {
                    end = start = getTextRangeBoundaryPosition(this.textRange, rangeContainerElement, true,
                        true).boundaryPosition;
                } else {
                    startBoundary = getTextRangeBoundaryPosition(this.textRange, rangeContainerElement, true, false);
                    start = startBoundary.boundaryPosition;

                    // An optimization used here is that if the start and end boundaries have the same parent element, the
                    // search scope for the end boundary can be limited to exclude the portion of the element that precedes
                    // the start boundary
                    end = getTextRangeBoundaryPosition(this.textRange, rangeContainerElement, false, false,
                        startBoundary.nodeInfo).boundaryPosition;
                }

                this.setStart(start.node, start.offset);
                this.setEnd(end.node, end.offset);
            };

            WrappedTextRange.prototype.getName = function() {
                return "WrappedTextRange";
            };

            DomRange.copyComparisonConstants(WrappedTextRange);

            var rangeToTextRange = function(range) {
                if (range.collapsed) {
                    return createBoundaryTextRange(new DomPosition(range.startContainer, range.startOffset), true);
                } else {
                    var startRange = createBoundaryTextRange(new DomPosition(range.startContainer, range.startOffset), true);
                    var endRange = createBoundaryTextRange(new DomPosition(range.endContainer, range.endOffset), false);
                    var textRange = getBody( DomRange.getRangeDocument(range) ).createTextRange();
                    textRange.setEndPoint("StartToStart", startRange);
                    textRange.setEndPoint("EndToEnd", endRange);
                    return textRange;
                }
            };

            WrappedTextRange.rangeToTextRange = rangeToTextRange;

            WrappedTextRange.prototype.toTextRange = function() {
                return rangeToTextRange(this);
            };

            api.WrappedTextRange = WrappedTextRange;

            // IE 9 and above have both implementations and Rangy makes both available. The next few lines sets which
            // implementation to use by default.
            if (!api.features.implementsDomRange || api.config.preferTextRange) {
                // Add WrappedTextRange as the Range property of the global object to allow expression like Range.END_TO_END to work
                var globalObj = (function(f) { return f("return this;")(); })(Function);
                if (typeof globalObj.Range == "undefined") {
                    globalObj.Range = WrappedTextRange;
                }

                api.createNativeRange = function(doc) {
                    doc = getContentDocument(doc, module, "createNativeRange");
                    return getBody(doc).createTextRange();
                };

                api.WrappedRange = WrappedTextRange;
            }
        }

        api.createRange = function(doc) {
            doc = getContentDocument(doc, module, "createRange");
            return new api.WrappedRange(api.createNativeRange(doc));
        };

        api.createRangyRange = function(doc) {
            doc = getContentDocument(doc, module, "createRangyRange");
            return new DomRange(doc);
        };

        util.createAliasForDeprecatedMethod(api, "createIframeRange", "createRange");
        util.createAliasForDeprecatedMethod(api, "createIframeRangyRange", "createRangyRange");

        api.addShimListener(function(win) {
            var doc = win.document;
            if (typeof doc.createRange == "undefined") {
                doc.createRange = function() {
                    return api.createRange(doc);
                };
            }
            doc = win = null;
        });
    });

    /*----------------------------------------------------------------------------------------------------------------*/

    // This module creates a selection object wrapper that conforms as closely as possible to the Selection specification
    // in the W3C Selection API spec (https://www.w3.org/TR/selection-api)
    api.createCoreModule("WrappedSelection", ["DomRange", "WrappedRange"], function(api, module) {
        api.config.checkSelectionRanges = true;

        var BOOLEAN = "boolean";
        var NUMBER = "number";
        var dom = api.dom;
        var util = api.util;
        var isHostMethod = util.isHostMethod;
        var DomRange = api.DomRange;
        var WrappedRange = api.WrappedRange;
        var DOMException = api.DOMException;
        var DomPosition = dom.DomPosition;
        var getNativeSelection;
        var selectionIsCollapsed;
        var features = api.features;
        var CONTROL = "Control";
        var getDocument = dom.getDocument;
        var getBody = dom.getBody;
        var rangesEqual = DomRange.rangesEqual;


        // Utility function to support direction parameters in the API that may be a string ("backward", "backwards",
        // "forward" or "forwards") or a Boolean (true for backwards).
        function isDirectionBackward(dir) {
            return (typeof dir == "string") ? /^backward(s)?$/i.test(dir) : !!dir;
        }

        function getWindow(win, methodName) {
            if (!win) {
                return window;
            } else if (dom.isWindow(win)) {
                return win;
            } else if (win instanceof WrappedSelection) {
                return win.win;
            } else {
                var doc = dom.getContentDocument(win, module, methodName);
                return dom.getWindow(doc);
            }
        }

        function getWinSelection(winParam) {
            return getWindow(winParam, "getWinSelection").getSelection();
        }

        function getDocSelection(winParam) {
            return getWindow(winParam, "getDocSelection").document.selection;
        }

        function winSelectionIsBackward(sel) {
            var backward = false;
            if (sel.anchorNode) {
                backward = (dom.comparePoints(sel.anchorNode, sel.anchorOffset, sel.focusNode, sel.focusOffset) == 1);
            }
            return backward;
        }

        // Test for the Range/TextRange and Selection features required
        // Test for ability to retrieve selection
        var implementsWinGetSelection = isHostMethod(window, "getSelection"),
            implementsDocSelection = util.isHostObject(document, "selection");

        features.implementsWinGetSelection = implementsWinGetSelection;
        features.implementsDocSelection = implementsDocSelection;

        var useDocumentSelection = implementsDocSelection && (!implementsWinGetSelection || api.config.preferTextRange);

        if (useDocumentSelection) {
            getNativeSelection = getDocSelection;
            api.isSelectionValid = function(winParam) {
                var doc = getWindow(winParam, "isSelectionValid").document, nativeSel = doc.selection;

                // Check whether the selection TextRange is actually contained within the correct document
                return (nativeSel.type != "None" || getDocument(nativeSel.createRange().parentElement()) == doc);
            };
        } else if (implementsWinGetSelection) {
            getNativeSelection = getWinSelection;
            api.isSelectionValid = function() {
                return true;
            };
        } else {
            module.fail("Neither document.selection or window.getSelection() detected.");
            return false;
        }

        api.getNativeSelection = getNativeSelection;

        var testSelection = getNativeSelection();

        // In Firefox, the selection is null in an iframe with display: none. See issue #138.
        if (!testSelection) {
            module.fail("Native selection was null (possibly issue 138?)");
            return false;
        }

        var testRange = api.createNativeRange(document);
        var body = getBody(document);

        // Obtaining a range from a selection
        var selectionHasAnchorAndFocus = util.areHostProperties(testSelection,
            ["anchorNode", "focusNode", "anchorOffset", "focusOffset"]);

        features.selectionHasAnchorAndFocus = selectionHasAnchorAndFocus;

        // Test for existence of native selection extend() method
        var selectionHasExtend = isHostMethod(testSelection, "extend");
        features.selectionHasExtend = selectionHasExtend;

        // Test for existence of native selection setBaseAndExtent() method
        var selectionHasSetBaseAndExtent = isHostMethod(testSelection, "setBaseAndExtent");
        features.selectionHasSetBaseAndExtent = selectionHasSetBaseAndExtent;

        // Test if rangeCount exists
        var selectionHasRangeCount = (typeof testSelection.rangeCount == NUMBER);
        features.selectionHasRangeCount = selectionHasRangeCount;

        var selectionSupportsMultipleRanges = false;
        var collapsedNonEditableSelectionsSupported = true;

        var addRangeBackwardToNative = selectionHasExtend ?
            function(nativeSelection, range) {
                var doc = DomRange.getRangeDocument(range);
                var endRange = api.createRange(doc);
                endRange.collapseToPoint(range.endContainer, range.endOffset);
                nativeSelection.addRange(getNativeRange(endRange));
                nativeSelection.extend(range.startContainer, range.startOffset);
            } : null;

        if (util.areHostMethods(testSelection, ["addRange", "getRangeAt", "removeAllRanges"]) &&
                typeof testSelection.rangeCount == NUMBER && features.implementsDomRange) {

            (function() {
                // Previously an iframe was used but this caused problems in some circumstances in IE, so tests are
                // performed on the current document's selection. See issue 109.

                // Note also that if a selection previously existed, it is wiped and later restored by these tests. This
                // will result in the selection direction being reversed if the original selection was backwards and the
                // browser does not support setting backwards selections (Internet Explorer, I'm looking at you).
                var sel = window.getSelection();
                if (sel) {
                    // Store the current selection
                    var originalSelectionRangeCount = sel.rangeCount;
                    var selectionHasMultipleRanges = (originalSelectionRangeCount > 1);
                    var originalSelectionRanges = [];
                    var originalSelectionBackward = winSelectionIsBackward(sel);
                    for (var i = 0; i < originalSelectionRangeCount; ++i) {
                        originalSelectionRanges[i] = sel.getRangeAt(i);
                    }

                    // Create some test elements
                    var testEl = dom.createTestElement(document, "", false);
                    var textNode = testEl.appendChild( document.createTextNode("\u00a0\u00a0\u00a0") );

                    // Test whether the native selection will allow a collapsed selection within a non-editable element
                    var r1 = document.createRange();

                    r1.setStart(textNode, 1);
                    r1.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(r1);
                    collapsedNonEditableSelectionsSupported = (sel.rangeCount == 1);
                    sel.removeAllRanges();

                    // Test whether the native selection is capable of supporting multiple ranges.
                    if (!selectionHasMultipleRanges) {
                        // Doing the original feature test here in Chrome 36 (and presumably later versions) prints a
                        // console error of "Discontiguous selection is not supported." that cannot be suppressed. There's
                        // nothing we can do about this while retaining the feature test so we have to resort to a browser
                        // sniff. I'm not happy about it. See
                        // https://code.google.com/p/chromium/issues/detail?id=399791
                        var chromeMatch = window.navigator.appVersion.match(/Chrome\/(.*?) /);
                        if (chromeMatch && parseInt(chromeMatch[1]) >= 36) {
                            selectionSupportsMultipleRanges = false;
                        } else {
                            var r2 = r1.cloneRange();
                            r1.setStart(textNode, 0);
                            r2.setEnd(textNode, 3);
                            r2.setStart(textNode, 2);
                            sel.addRange(r1);
                            sel.addRange(r2);
                            selectionSupportsMultipleRanges = (sel.rangeCount == 2);
                        }
                    }

                    // Clean up
                    dom.removeNode(testEl);
                    sel.removeAllRanges();

                    for (i = 0; i < originalSelectionRangeCount; ++i) {
                        if (i == 0 && originalSelectionBackward) {
                            if (addRangeBackwardToNative) {
                                addRangeBackwardToNative(sel, originalSelectionRanges[i]);
                            } else {
                                api.warn("Rangy initialization: original selection was backwards but selection has been restored forwards because the browser does not support Selection.extend");
                                sel.addRange(originalSelectionRanges[i]);
                            }
                        } else {
                            sel.addRange(originalSelectionRanges[i]);
                        }
                    }
                }
            })();
        }

        features.selectionSupportsMultipleRanges = selectionSupportsMultipleRanges;
        features.collapsedNonEditableSelectionsSupported = collapsedNonEditableSelectionsSupported;

        // ControlRanges
        var implementsControlRange = false, testControlRange;

        if (body && isHostMethod(body, "createControlRange")) {
            testControlRange = body.createControlRange();
            if (util.areHostProperties(testControlRange, ["item", "add"])) {
                implementsControlRange = true;
            }
        }
        features.implementsControlRange = implementsControlRange;

        // Selection collapsedness
        if (selectionHasAnchorAndFocus) {
            selectionIsCollapsed = function(sel) {
                return sel.anchorNode === sel.focusNode && sel.anchorOffset === sel.focusOffset;
            };
        } else {
            selectionIsCollapsed = function(sel) {
                return sel.rangeCount ? sel.getRangeAt(sel.rangeCount - 1).collapsed : false;
            };
        }

        function updateAnchorAndFocusFromRange(sel, range, backward) {
            var anchorPrefix = backward ? "end" : "start", focusPrefix = backward ? "start" : "end";
            sel.anchorNode = range[anchorPrefix + "Container"];
            sel.anchorOffset = range[anchorPrefix + "Offset"];
            sel.focusNode = range[focusPrefix + "Container"];
            sel.focusOffset = range[focusPrefix + "Offset"];
        }

        function updateAnchorAndFocusFromNativeSelection(sel) {
            var nativeSel = sel.nativeSelection;
            sel.anchorNode = nativeSel.anchorNode;
            sel.anchorOffset = nativeSel.anchorOffset;
            sel.focusNode = nativeSel.focusNode;
            sel.focusOffset = nativeSel.focusOffset;
        }

        function updateEmptySelection(sel) {
            sel.anchorNode = sel.focusNode = null;
            sel.anchorOffset = sel.focusOffset = 0;
            sel.rangeCount = 0;
            sel.isCollapsed = true;
            sel._ranges.length = 0;
            updateType(sel);
        }

        function updateType(sel) {
            sel.type = (sel.rangeCount == 0) ? "None" : (selectionIsCollapsed(sel) ? "Caret" : "Range");
        }

        function getNativeRange(range) {
            var nativeRange;
            if (range instanceof DomRange) {
                nativeRange = api.createNativeRange(range.getDocument());
                nativeRange.setEnd(range.endContainer, range.endOffset);
                nativeRange.setStart(range.startContainer, range.startOffset);
            } else if (range instanceof WrappedRange) {
                nativeRange = range.nativeRange;
            } else if (features.implementsDomRange && (range instanceof dom.getWindow(range.startContainer).Range)) {
                nativeRange = range;
            }
            return nativeRange;
        }

        function rangeContainsSingleElement(rangeNodes) {
            if (!rangeNodes.length || rangeNodes[0].nodeType != 1) {
                return false;
            }
            for (var i = 1, len = rangeNodes.length; i < len; ++i) {
                if (!dom.isAncestorOf(rangeNodes[0], rangeNodes[i])) {
                    return false;
                }
            }
            return true;
        }

        function getSingleElementFromRange(range) {
            var nodes = range.getNodes();
            if (!rangeContainsSingleElement(nodes)) {
                throw module.createError("getSingleElementFromRange: range " + range.inspect() + " did not consist of a single element");
            }
            return nodes[0];
        }

        // Simple, quick test which only needs to distinguish between a TextRange and a ControlRange
        function isTextRange(range) {
            return !!range && typeof range.text != "undefined";
        }

        function updateFromTextRange(sel, range) {
            // Create a Range from the selected TextRange
            var wrappedRange = new WrappedRange(range);
            sel._ranges = [wrappedRange];

            updateAnchorAndFocusFromRange(sel, wrappedRange, false);
            sel.rangeCount = 1;
            sel.isCollapsed = wrappedRange.collapsed;
            updateType(sel);
        }

        function updateControlSelection(sel) {
            // Update the wrapped selection based on what's now in the native selection
            sel._ranges.length = 0;
            if (sel.docSelection.type == "None") {
                updateEmptySelection(sel);
            } else {
                var controlRange = sel.docSelection.createRange();
                if (isTextRange(controlRange)) {
                    // This case (where the selection type is "Control" and calling createRange() on the selection returns
                    // a TextRange) can happen in IE 9. It happens, for example, when all elements in the selected
                    // ControlRange have been removed from the ControlRange and removed from the document.
                    updateFromTextRange(sel, controlRange);
                } else {
                    sel.rangeCount = controlRange.length;
                    var range, doc = getDocument(controlRange.item(0));
                    for (var i = 0; i < sel.rangeCount; ++i) {
                        range = api.createRange(doc);
                        range.selectNode(controlRange.item(i));
                        sel._ranges.push(range);
                    }
                    sel.isCollapsed = sel.rangeCount == 1 && sel._ranges[0].collapsed;
                    updateAnchorAndFocusFromRange(sel, sel._ranges[sel.rangeCount - 1], false);
                    updateType(sel);
                }
            }
        }

        function addRangeToControlSelection(sel, range) {
            var controlRange = sel.docSelection.createRange();
            var rangeElement = getSingleElementFromRange(range);

            // Create a new ControlRange containing all the elements in the selected ControlRange plus the element
            // contained by the supplied range
            var doc = getDocument(controlRange.item(0));
            var newControlRange = getBody(doc).createControlRange();
            for (var i = 0, len = controlRange.length; i < len; ++i) {
                newControlRange.add(controlRange.item(i));
            }
            try {
                newControlRange.add(rangeElement);
            } catch (ex) {
                throw module.createError("addRange(): Element within the specified Range could not be added to control selection (does it have layout?)");
            }
            newControlRange.select();

            // Update the wrapped selection based on what's now in the native selection
            updateControlSelection(sel);
        }

        var getSelectionRangeAt;

        if (isHostMethod(testSelection, "getRangeAt")) {
            // try/catch is present because getRangeAt() must have thrown an error in some browser and some situation.
            // Unfortunately, I didn't write a comment about the specifics and am now scared to take it out. Let that be a
            // lesson to us all, especially me.
            getSelectionRangeAt = function(sel, index) {
                try {
                    return sel.getRangeAt(index);
                } catch (ex) {
                    return null;
                }
            };
        } else if (selectionHasAnchorAndFocus) {
            getSelectionRangeAt = function(sel) {
                var doc = getDocument(sel.anchorNode);
                var range = api.createRange(doc);
                range.setStartAndEnd(sel.anchorNode, sel.anchorOffset, sel.focusNode, sel.focusOffset);

                // Handle the case when the selection was selected backwards (from the end to the start in the
                // document)
                if (range.collapsed !== this.isCollapsed) {
                    range.setStartAndEnd(sel.focusNode, sel.focusOffset, sel.anchorNode, sel.anchorOffset);
                }

                return range;
            };
        }

        function WrappedSelection(selection, docSelection, win) {
            this.nativeSelection = selection;
            this.docSelection = docSelection;
            this._ranges = [];
            this.win = win;
            this.refresh();
        }

        WrappedSelection.prototype = api.selectionPrototype;

        function deleteProperties(sel) {
            sel.win = sel.anchorNode = sel.focusNode = sel._ranges = null;
            sel.rangeCount = sel.anchorOffset = sel.focusOffset = 0;
            sel.detached = true;
            updateType(sel);
        }

        var cachedRangySelections = [];

        function actOnCachedSelection(win, action) {
            var i = cachedRangySelections.length, cached, sel;
            while (i--) {
                cached = cachedRangySelections[i];
                sel = cached.selection;
                if (action == "deleteAll") {
                    deleteProperties(sel);
                } else if (cached.win == win) {
                    if (action == "delete") {
                        cachedRangySelections.splice(i, 1);
                        return true;
                    } else {
                        return sel;
                    }
                }
            }
            if (action == "deleteAll") {
                cachedRangySelections.length = 0;
            }
            return null;
        }

        var getSelection = function(win) {
            // Check if the parameter is a Rangy Selection object
            if (win && win instanceof WrappedSelection) {
                win.refresh();
                return win;
            }

            win = getWindow(win, "getNativeSelection");

            var sel = actOnCachedSelection(win);
            var nativeSel = getNativeSelection(win), docSel = implementsDocSelection ? getDocSelection(win) : null;
            if (sel) {
                sel.nativeSelection = nativeSel;
                sel.docSelection = docSel;
                sel.refresh();
            } else {
                sel = new WrappedSelection(nativeSel, docSel, win);
                cachedRangySelections.push( { win: win, selection: sel } );
            }
            return sel;
        };

        api.getSelection = getSelection;

        util.createAliasForDeprecatedMethod(api, "getIframeSelection", "getSelection");

        var selProto = WrappedSelection.prototype;

        function createControlSelection(sel, ranges) {
            // Ensure that the selection becomes of type "Control"
            var doc = getDocument(ranges[0].startContainer);
            var controlRange = getBody(doc).createControlRange();
            for (var i = 0, el, len = ranges.length; i < len; ++i) {
                el = getSingleElementFromRange(ranges[i]);
                try {
                    controlRange.add(el);
                } catch (ex) {
                    throw module.createError("setRanges(): Element within one of the specified Ranges could not be added to control selection (does it have layout?)");
                }
            }
            controlRange.select();

            // Update the wrapped selection based on what's now in the native selection
            updateControlSelection(sel);
        }

        // Selecting a range
        if (!useDocumentSelection && selectionHasAnchorAndFocus && util.areHostMethods(testSelection, ["removeAllRanges", "addRange"])) {
            selProto.removeAllRanges = function() {
                this.nativeSelection.removeAllRanges();
                updateEmptySelection(this);
            };

            var addRangeBackward = function(sel, range) {
                addRangeBackwardToNative(sel.nativeSelection, range);
                sel.refresh();
            };

            if (selectionHasRangeCount) {
                selProto.addRange = function(range, direction) {
                    if (implementsControlRange && implementsDocSelection && this.docSelection.type == CONTROL) {
                        addRangeToControlSelection(this, range);
                    } else {
                        if (isDirectionBackward(direction) && selectionHasExtend) {
                            addRangeBackward(this, range);
                        } else {
                            var previousRangeCount;
                            if (selectionSupportsMultipleRanges) {
                                previousRangeCount = this.rangeCount;
                            } else {
                                this.removeAllRanges();
                                previousRangeCount = 0;
                            }
                            // Clone the native range so that changing the selected range does not affect the selection.
                            // This is contrary to the spec but is the only way to achieve consistency between browsers. See
                            // issue 80.
                            var clonedNativeRange = getNativeRange(range).cloneRange();
                            try {
                                this.nativeSelection.addRange(clonedNativeRange);
                            } catch (ex) {
                            }

                            // Check whether adding the range was successful
                            this.rangeCount = this.nativeSelection.rangeCount;

                            if (this.rangeCount == previousRangeCount + 1) {
                                // The range was added successfully

                                // Check whether the range that we added to the selection is reflected in the last range extracted from
                                // the selection
                                if (api.config.checkSelectionRanges) {
                                    var nativeRange = getSelectionRangeAt(this.nativeSelection, this.rangeCount - 1);
                                    if (nativeRange && !rangesEqual(nativeRange, range)) {
                                        // Happens in WebKit with, for example, a selection placed at the start of a text node
                                        range = new WrappedRange(nativeRange);
                                    }
                                }
                                this._ranges[this.rangeCount - 1] = range;
                                updateAnchorAndFocusFromRange(this, range, selectionIsBackward(this.nativeSelection));
                                this.isCollapsed = selectionIsCollapsed(this);
                                updateType(this);
                            } else {
                                // The range was not added successfully. The simplest thing is to refresh
                                this.refresh();
                            }
                        }
                    }
                };
            } else {
                selProto.addRange = function(range, direction) {
                    if (isDirectionBackward(direction) && selectionHasExtend) {
                        addRangeBackward(this, range);
                    } else {
                        this.nativeSelection.addRange(getNativeRange(range));
                        this.refresh();
                    }
                };
            }

            selProto.setRanges = function(ranges) {
                if (implementsControlRange && implementsDocSelection && ranges.length > 1) {
                    createControlSelection(this, ranges);
                } else {
                    this.removeAllRanges();
                    for (var i = 0, len = ranges.length; i < len; ++i) {
                        this.addRange(ranges[i]);
                    }
                }
            };
        } else if (isHostMethod(testSelection, "empty") && isHostMethod(testRange, "select") &&
                   implementsControlRange && useDocumentSelection) {

            selProto.removeAllRanges = function() {
                // Added try/catch as fix for issue #21
                try {
                    this.docSelection.empty();

                    // Check for empty() not working (issue #24)
                    if (this.docSelection.type != "None") {
                        // Work around failure to empty a control selection by instead selecting a TextRange and then
                        // calling empty()
                        var doc;
                        if (this.anchorNode) {
                            doc = getDocument(this.anchorNode);
                        } else if (this.docSelection.type == CONTROL) {
                            var controlRange = this.docSelection.createRange();
                            if (controlRange.length) {
                                doc = getDocument( controlRange.item(0) );
                            }
                        }
                        if (doc) {
                            var textRange = getBody(doc).createTextRange();
                            textRange.select();
                            this.docSelection.empty();
                        }
                    }
                } catch(ex) {}
                updateEmptySelection(this);
            };

            selProto.addRange = function(range) {
                if (this.docSelection.type == CONTROL) {
                    addRangeToControlSelection(this, range);
                } else {
                    api.WrappedTextRange.rangeToTextRange(range).select();
                    this._ranges[0] = range;
                    this.rangeCount = 1;
                    this.isCollapsed = this._ranges[0].collapsed;
                    updateAnchorAndFocusFromRange(this, range, false);
                    updateType(this);
                }
            };

            selProto.setRanges = function(ranges) {
                this.removeAllRanges();
                var rangeCount = ranges.length;
                if (rangeCount > 1) {
                    createControlSelection(this, ranges);
                } else if (rangeCount) {
                    this.addRange(ranges[0]);
                }
            };
        } else {
            module.fail("No means of selecting a Range or TextRange was found");
            return false;
        }

        selProto.getRangeAt = function(index) {
            if (index < 0 || index >= this.rangeCount) {
                throw new DOMException("INDEX_SIZE_ERR");
            } else {
                // Clone the range to preserve selection-range independence. See issue 80.
                return this._ranges[index].cloneRange();
            }
        };

        var refreshSelection;

        if (useDocumentSelection) {
            refreshSelection = function(sel) {
                var range;
                if (api.isSelectionValid(sel.win)) {
                    range = sel.docSelection.createRange();
                } else {
                    range = getBody(sel.win.document).createTextRange();
                    range.collapse(true);
                }

                if (sel.docSelection.type == CONTROL) {
                    updateControlSelection(sel);
                } else if (isTextRange(range)) {
                    updateFromTextRange(sel, range);
                } else {
                    updateEmptySelection(sel);
                }
            };
        } else if (isHostMethod(testSelection, "getRangeAt") && typeof testSelection.rangeCount == NUMBER) {
            refreshSelection = function(sel) {
                if (implementsControlRange && implementsDocSelection && sel.docSelection.type == CONTROL) {
                    updateControlSelection(sel);
                } else {
                    sel._ranges.length = sel.rangeCount = sel.nativeSelection.rangeCount;
                    if (sel.rangeCount) {
                        for (var i = 0, len = sel.rangeCount; i < len; ++i) {
                            sel._ranges[i] = new api.WrappedRange(sel.nativeSelection.getRangeAt(i));
                        }
                        updateAnchorAndFocusFromRange(sel, sel._ranges[sel.rangeCount - 1], selectionIsBackward(sel.nativeSelection));
                        sel.isCollapsed = selectionIsCollapsed(sel);
                        updateType(sel);
                    } else {
                        updateEmptySelection(sel);
                    }
                }
            };
        } else if (selectionHasAnchorAndFocus && typeof testSelection.isCollapsed == BOOLEAN && typeof testRange.collapsed == BOOLEAN && features.implementsDomRange) {
            refreshSelection = function(sel) {
                var range, nativeSel = sel.nativeSelection;
                if (nativeSel.anchorNode) {
                    range = getSelectionRangeAt(nativeSel, 0);
                    sel._ranges = [range];
                    sel.rangeCount = 1;
                    updateAnchorAndFocusFromNativeSelection(sel);
                    sel.isCollapsed = selectionIsCollapsed(sel);
                    updateType(sel);
                } else {
                    updateEmptySelection(sel);
                }
            };
        } else {
            module.fail("No means of obtaining a Range or TextRange from the user's selection was found");
            return false;
        }

        selProto.refresh = function(checkForChanges) {
            var oldRanges = checkForChanges ? this._ranges.slice(0) : null;
            var oldAnchorNode = this.anchorNode, oldAnchorOffset = this.anchorOffset;

            refreshSelection(this);
            if (checkForChanges) {
                // Check the range count first
                var i = oldRanges.length;
                if (i != this._ranges.length) {
                    return true;
                }

                // Now check the direction. Checking the anchor position is the same is enough since we're checking all the
                // ranges after this
                if (this.anchorNode != oldAnchorNode || this.anchorOffset != oldAnchorOffset) {
                    return true;
                }

                // Finally, compare each range in turn
                while (i--) {
                    if (!rangesEqual(oldRanges[i], this._ranges[i])) {
                        return true;
                    }
                }
                return false;
            }
        };

        // Removal of a single range
        var removeRangeManually = function(sel, range) {
            var ranges = sel.getAllRanges();
            sel.removeAllRanges();
            for (var i = 0, len = ranges.length; i < len; ++i) {
                if (!rangesEqual(range, ranges[i])) {
                    sel.addRange(ranges[i]);
                }
            }
            if (!sel.rangeCount) {
                updateEmptySelection(sel);
            }
        };

        if (implementsControlRange && implementsDocSelection) {
            selProto.removeRange = function(range) {
                if (this.docSelection.type == CONTROL) {
                    var controlRange = this.docSelection.createRange();
                    var rangeElement = getSingleElementFromRange(range);

                    // Create a new ControlRange containing all the elements in the selected ControlRange minus the
                    // element contained by the supplied range
                    var doc = getDocument(controlRange.item(0));
                    var newControlRange = getBody(doc).createControlRange();
                    var el, removed = false;
                    for (var i = 0, len = controlRange.length; i < len; ++i) {
                        el = controlRange.item(i);
                        if (el !== rangeElement || removed) {
                            newControlRange.add(controlRange.item(i));
                        } else {
                            removed = true;
                        }
                    }
                    newControlRange.select();

                    // Update the wrapped selection based on what's now in the native selection
                    updateControlSelection(this);
                } else {
                    removeRangeManually(this, range);
                }
            };
        } else {
            selProto.removeRange = function(range) {
                removeRangeManually(this, range);
            };
        }

        // Detecting if a selection is backward
        var selectionIsBackward;
        if (!useDocumentSelection && selectionHasAnchorAndFocus && features.implementsDomRange) {
            selectionIsBackward = winSelectionIsBackward;

            selProto.isBackward = function() {
                return selectionIsBackward(this);
            };
        } else {
            selectionIsBackward = selProto.isBackward = function() {
                return false;
            };
        }

        // Create an alias for backwards compatibility. From 1.3, everything is "backward" rather than "backwards"
        selProto.isBackwards = selProto.isBackward;

        // Selection stringifier
        // This is conformant to the old HTML5 selections draft spec but differs from WebKit and Mozilla's implementation.
        // The current spec does not yet define this method.
        selProto.toString = function() {
            var rangeTexts = [];
            for (var i = 0, len = this.rangeCount; i < len; ++i) {
                rangeTexts[i] = "" + this._ranges[i];
            }
            return rangeTexts.join("");
        };

        function assertNodeInSameDocument(sel, node) {
            if (sel.win.document != getDocument(node)) {
                throw new DOMException("WRONG_DOCUMENT_ERR");
            }
        }

        function assertValidOffset(node, offset) {
            if (offset < 0 || offset > (dom.isCharacterDataNode(node) ? node.length : node.childNodes.length)) {
                throw new DOMException("INDEX_SIZE_ERR");
            }
        }

        // No current browser conforms fully to the spec for this method, so Rangy's own method is always used
        selProto.collapse = function(node, offset) {
            assertNodeInSameDocument(this, node);
            var range = api.createRange(node);
            range.collapseToPoint(node, offset);
            this.setSingleRange(range);
            this.isCollapsed = true;
        };

        selProto.collapseToStart = function() {
            if (this.rangeCount) {
                var range = this._ranges[0];
                this.collapse(range.startContainer, range.startOffset);
            } else {
                throw new DOMException("INVALID_STATE_ERR");
            }
        };

        selProto.collapseToEnd = function() {
            if (this.rangeCount) {
                var range = this._ranges[this.rangeCount - 1];
                this.collapse(range.endContainer, range.endOffset);
            } else {
                throw new DOMException("INVALID_STATE_ERR");
            }
        };

        // The spec is very specific on how selectAllChildren should be implemented and not all browsers implement it as
        // specified so the native implementation is never used by Rangy.
        selProto.selectAllChildren = function(node) {
            assertNodeInSameDocument(this, node);
            var range = api.createRange(node);
            range.selectNodeContents(node);
            this.setSingleRange(range);
        };

        if (selectionHasSetBaseAndExtent) {
            selProto.setBaseAndExtent = function(anchorNode, anchorOffset, focusNode, focusOffset) {
                this.nativeSelection.setBaseAndExtent(anchorNode, anchorOffset, focusNode, focusOffset);
                this.refresh();
            };
        } else if (selectionHasExtend) {
            selProto.setBaseAndExtent = function(anchorNode, anchorOffset, focusNode, focusOffset) {
                assertValidOffset(anchorNode, anchorOffset);
                assertValidOffset(focusNode, focusOffset);
                assertNodeInSameDocument(this, anchorNode);
                assertNodeInSameDocument(this, focusNode);
                var range = api.createRange(node);
                var isBackwards = (dom.comparePoints(anchorNode, anchorOffset, focusNode, focusOffset) == -1);
                if (isBackwards) {
                    range.setStartAndEnd(focusNode, focusOffset, anchorNode, anchorOffset);
                } else {
                    range.setStartAndEnd(anchorNode, anchorOffset, focusNode, focusOffset);
                }
                this.setSingleRange(range, isBackwards);
            };
        }

        selProto.deleteFromDocument = function() {
            // Sepcial behaviour required for IE's control selections
            if (implementsControlRange && implementsDocSelection && this.docSelection.type == CONTROL) {
                var controlRange = this.docSelection.createRange();
                var element;
                while (controlRange.length) {
                    element = controlRange.item(0);
                    controlRange.remove(element);
                    dom.removeNode(element);
                }
                this.refresh();
            } else if (this.rangeCount) {
                var ranges = this.getAllRanges();
                if (ranges.length) {
                    this.removeAllRanges();
                    for (var i = 0, len = ranges.length; i < len; ++i) {
                        ranges[i].deleteContents();
                    }
                    // The spec says nothing about what the selection should contain after calling deleteContents on each
                    // range. Firefox moves the selection to where the final selected range was, so we emulate that
                    this.addRange(ranges[len - 1]);
                }
            }
        };

        // The following are non-standard extensions
        selProto.eachRange = function(func, returnValue) {
            for (var i = 0, len = this._ranges.length; i < len; ++i) {
                if ( func( this.getRangeAt(i) ) ) {
                    return returnValue;
                }
            }
        };

        selProto.getAllRanges = function() {
            var ranges = [];
            this.eachRange(function(range) {
                ranges.push(range);
            });
            return ranges;
        };

        selProto.setSingleRange = function(range, direction) {
            this.removeAllRanges();
            this.addRange(range, direction);
        };

        selProto.callMethodOnEachRange = function(methodName, params) {
            var results = [];
            this.eachRange( function(range) {
                results.push( range[methodName].apply(range, params || []) );
            } );
            return results;
        };

        function createStartOrEndSetter(isStart) {
            return function(node, offset) {
                var range;
                if (this.rangeCount) {
                    range = this.getRangeAt(0);
                    range["set" + (isStart ? "Start" : "End")](node, offset);
                } else {
                    range = api.createRange(this.win.document);
                    range.setStartAndEnd(node, offset);
                }
                this.setSingleRange(range, this.isBackward());
            };
        }

        selProto.setStart = createStartOrEndSetter(true);
        selProto.setEnd = createStartOrEndSetter(false);

        // Add select() method to Range prototype. Any existing selection will be removed.
        api.rangePrototype.select = function(direction) {
            getSelection( this.getDocument() ).setSingleRange(this, direction);
        };

        selProto.changeEachRange = function(func) {
            var ranges = [];
            var backward = this.isBackward();

            this.eachRange(function(range) {
                func(range);
                ranges.push(range);
            });

            this.removeAllRanges();
            if (backward && ranges.length == 1) {
                this.addRange(ranges[0], "backward");
            } else {
                this.setRanges(ranges);
            }
        };

        selProto.containsNode = function(node, allowPartial) {
            return this.eachRange( function(range) {
                return range.containsNode(node, allowPartial);
            }, true ) || false;
        };

        selProto.getBookmark = function(containerNode) {
            return {
                backward: this.isBackward(),
                rangeBookmarks: this.callMethodOnEachRange("getBookmark", [containerNode])
            };
        };

        selProto.moveToBookmark = function(bookmark) {
            var selRanges = [];
            for (var i = 0, rangeBookmark, range; rangeBookmark = bookmark.rangeBookmarks[i++]; ) {
                range = api.createRange(this.win);
                range.moveToBookmark(rangeBookmark);
                selRanges.push(range);
            }
            if (bookmark.backward) {
                this.setSingleRange(selRanges[0], "backward");
            } else {
                this.setRanges(selRanges);
            }
        };

        selProto.saveRanges = function() {
            return {
                backward: this.isBackward(),
                ranges: this.callMethodOnEachRange("cloneRange")
            };
        };

        selProto.restoreRanges = function(selRanges) {
            this.removeAllRanges();
            for (var i = 0, range; range = selRanges.ranges[i]; ++i) {
                this.addRange(range, (selRanges.backward && i == 0));
            }
        };

        selProto.toHtml = function() {
            var rangeHtmls = [];
            this.eachRange(function(range) {
                rangeHtmls.push( DomRange.toHtml(range) );
            });
            return rangeHtmls.join("");
        };

        if (features.implementsTextRange) {
            selProto.getNativeTextRange = function() {
                var sel, textRange;
                if ( (sel = this.docSelection) ) {
                    var range = sel.createRange();
                    if (isTextRange(range)) {
                        return range;
                    } else {
                        throw module.createError("getNativeTextRange: selection is a control selection");
                    }
                } else if (this.rangeCount > 0) {
                    return api.WrappedTextRange.rangeToTextRange( this.getRangeAt(0) );
                } else {
                    throw module.createError("getNativeTextRange: selection contains no range");
                }
            };
        }

        function inspect(sel) {
            var rangeInspects = [];
            var anchor = new DomPosition(sel.anchorNode, sel.anchorOffset);
            var focus = new DomPosition(sel.focusNode, sel.focusOffset);
            var name = (typeof sel.getName == "function") ? sel.getName() : "Selection";

            if (typeof sel.rangeCount != "undefined") {
                for (var i = 0, len = sel.rangeCount; i < len; ++i) {
                    rangeInspects[i] = DomRange.inspect(sel.getRangeAt(i));
                }
            }
            return "[" + name + "(Ranges: " + rangeInspects.join(", ") +
                    ")(anchor: " + anchor.inspect() + ", focus: " + focus.inspect() + "]";
        }

        selProto.getName = function() {
            return "WrappedSelection";
        };

        selProto.inspect = function() {
            return inspect(this);
        };

        selProto.detach = function() {
            actOnCachedSelection(this.win, "delete");
            deleteProperties(this);
        };

        WrappedSelection.detachAll = function() {
            actOnCachedSelection(null, "deleteAll");
        };

        WrappedSelection.inspect = inspect;
        WrappedSelection.isDirectionBackward = isDirectionBackward;

        api.Selection = WrappedSelection;

        api.selectionPrototype = selProto;

        api.addShimListener(function(win) {
            if (typeof win.getSelection == "undefined") {
                win.getSelection = function() {
                    return getSelection(win);
                };
            }
            win = null;
        });
    });
    

    /*----------------------------------------------------------------------------------------------------------------*/

    // Wait for document to load before initializing
    var docReady = false;

    var loadHandler = function(e) {
        if (!docReady) {
            docReady = true;
            if (!api.initialized && api.config.autoInitialize) {
                init();
            }
        }
    };

    if (isBrowser) {
        // Test whether the document has already been loaded and initialize immediately if so
        if (document.readyState == "complete") {
            loadHandler();
        } else {
            if (isHostMethod(document, "addEventListener")) {
                document.addEventListener("DOMContentLoaded", loadHandler, false);
            }

            // Add a fallback in case the DOMContentLoaded event isn't supported
            addListener(window, "load", loadHandler);
        }
    }

    return api;
}, this);
/**
 * Class Applier module for Rangy.
 * Adds, removes and toggles classes on Ranges and Selections
 *
 * Part of Rangy, a cross-browser JavaScript range and selection library
 * https://github.com/timdown/rangy
 *
 * Depends on Rangy core.
 *
 * Copyright 2024, Tim Down
 * Licensed under the MIT license.
 * Version: 1.3.2
 * Build date: 2 November 2024
 */
(function(factory, root) {
    if (typeof define == "function" && define.amd) {
        // AMD. Register as an anonymous module with a dependency on Rangy.
        define(["./rangy-core"], factory);
    } else if (typeof module != "undefined" && typeof exports == "object") {
        // Node/CommonJS style
        module.exports = factory( require("rangy") );
    } else {
        // No AMD or CommonJS support so we use the rangy property of root (probably the global variable)
        factory(root.rangy);
    }
})(function(rangy) {
    rangy.createModule("ClassApplier", ["WrappedSelection"], function(api, module) {
        var dom = api.dom;
        var DomPosition = dom.DomPosition;
        var contains = dom.arrayContains;
        var util = api.util;
        var forEach = util.forEach;


        var defaultTagName = "span";
        var createElementNSSupported = util.isHostMethod(document, "createElementNS");

        function each(obj, func) {
            for (var i in obj) {
                if (obj.hasOwnProperty(i)) {
                    if (func(i, obj[i]) === false) {
                        return false;
                    }
                }
            }
            return true;
        }

        function trim(str) {
            return str.replace(/^\s\s*/, "").replace(/\s\s*$/, "");
        }

        function classNameContainsClass(fullClassName, className) {
            return !!fullClassName && new RegExp("(?:^|\\s)" + className + "(?:\\s|$)").test(fullClassName);
        }

        // Inefficient, inelegant nonsense for IE's svg element, which has no classList and non-HTML className implementation
        function hasClass(el, className) {
            if (typeof el.classList == "object") {
                return el.classList.contains(className);
            } else {
                var classNameSupported = (typeof el.className == "string");
                var elClass = classNameSupported ? el.className : el.getAttribute("class");
                return classNameContainsClass(elClass, className);
            }
        }

        function addClass(el, className) {
            if (typeof el.classList == "object") {
                el.classList.add(className);
            } else {
                var classNameSupported = (typeof el.className == "string");
                var elClass = classNameSupported ? el.className : el.getAttribute("class");
                if (elClass) {
                    if (!classNameContainsClass(elClass, className)) {
                        elClass += " " + className;
                    }
                } else {
                    elClass = className;
                }
                if (classNameSupported) {
                    el.className = elClass;
                } else {
                    el.setAttribute("class", elClass);
                }
            }
        }

        var removeClass = (function() {
            function replacer(matched, whiteSpaceBefore, whiteSpaceAfter) {
                return (whiteSpaceBefore && whiteSpaceAfter) ? " " : "";
            }

            return function(el, className) {
                if (typeof el.classList == "object") {
                    el.classList.remove(className);
                } else {
                    var classNameSupported = (typeof el.className == "string");
                    var elClass = classNameSupported ? el.className : el.getAttribute("class");
                    elClass = elClass.replace(new RegExp("(^|\\s)" + className + "(\\s|$)"), replacer);
                    if (classNameSupported) {
                        el.className = elClass;
                    } else {
                        el.setAttribute("class", elClass);
                    }
                }
            };
        })();

        function getClass(el) {
            var classNameSupported = (typeof el.className == "string");
            return classNameSupported ? el.className : el.getAttribute("class");
        }

        function sortClassName(className) {
            return className && className.split(/\s+/).sort().join(" ");
        }

        function getSortedClassName(el) {
            return sortClassName( getClass(el) );
        }

        function haveSameClasses(el1, el2) {
            return getSortedClassName(el1) == getSortedClassName(el2);
        }

        function hasAllClasses(el, className) {
            var classes = className.split(/\s+/);
            for (var i = 0, len = classes.length; i < len; ++i) {
                if (!hasClass(el, trim(classes[i]))) {
                    return false;
                }
            }
            return true;
        }

        function canTextBeStyled(textNode) {
            var parent = textNode.parentNode;
            return (parent && parent.nodeType == 1 && !/^(textarea|style|script|select|iframe)$/i.test(parent.nodeName));
        }

        function movePosition(position, oldParent, oldIndex, newParent, newIndex) {
            var posNode = position.node, posOffset = position.offset;
            var newNode = posNode, newOffset = posOffset;

            if (posNode == newParent && posOffset > newIndex) {
                ++newOffset;
            }

            if (posNode == oldParent && (posOffset == oldIndex  || posOffset == oldIndex + 1)) {
                newNode = newParent;
                newOffset += newIndex - oldIndex;
            }

            if (posNode == oldParent && posOffset > oldIndex + 1) {
                --newOffset;
            }

            position.node = newNode;
            position.offset = newOffset;
        }

        function movePositionWhenRemovingNode(position, parentNode, index) {
            if (position.node == parentNode && position.offset > index) {
                --position.offset;
            }
        }

        function movePreservingPositions(node, newParent, newIndex, positionsToPreserve) {
            // For convenience, allow newIndex to be -1 to mean "insert at the end".
            if (newIndex == -1) {
                newIndex = newParent.childNodes.length;
            }

            var oldParent = node.parentNode;
            var oldIndex = dom.getNodeIndex(node);

            forEach(positionsToPreserve, function(position) {
                movePosition(position, oldParent, oldIndex, newParent, newIndex);
            });

            // Now actually move the node.
            if (newParent.childNodes.length == newIndex) {
                newParent.appendChild(node);
            } else {
                newParent.insertBefore(node, newParent.childNodes[newIndex]);
            }
        }

        function removePreservingPositions(node, positionsToPreserve) {

            var oldParent = node.parentNode;
            var oldIndex = dom.getNodeIndex(node);

            forEach(positionsToPreserve, function(position) {
                movePositionWhenRemovingNode(position, oldParent, oldIndex);
            });

            dom.removeNode(node);
        }

        function moveChildrenPreservingPositions(node, newParent, newIndex, removeNode, positionsToPreserve) {
            var child, children = [];
            while ( (child = node.firstChild) ) {
                movePreservingPositions(child, newParent, newIndex++, positionsToPreserve);
                children.push(child);
            }
            if (removeNode) {
                removePreservingPositions(node, positionsToPreserve);
            }
            return children;
        }

        function replaceWithOwnChildrenPreservingPositions(element, positionsToPreserve) {
            return moveChildrenPreservingPositions(element, element.parentNode, dom.getNodeIndex(element), true, positionsToPreserve);
        }

        function rangeSelectsAnyText(range, textNode) {
            var textNodeRange = range.cloneRange();
            textNodeRange.selectNodeContents(textNode);

            var intersectionRange = textNodeRange.intersection(range);
            var text = intersectionRange ? intersectionRange.toString() : "";

            return text != "";
        }

        function getEffectiveTextNodes(range) {
            var nodes = range.getNodes([3]);

            // Optimization as per issue 145

            // Remove non-intersecting text nodes from the start of the range
            var start = 0, node;
            while ( (node = nodes[start]) && !rangeSelectsAnyText(range, node) ) {
                ++start;
            }

            // Remove non-intersecting text nodes from the start of the range
            var end = nodes.length - 1;
            while ( (node = nodes[end]) && !rangeSelectsAnyText(range, node) ) {
                --end;
            }

            return nodes.slice(start, end + 1);
        }

        function elementsHaveSameNonClassAttributes(el1, el2) {
            if (el1.attributes.length != el2.attributes.length) return false;
            for (var i = 0, len = el1.attributes.length, attr1, attr2, name; i < len; ++i) {
                attr1 = el1.attributes[i];
                name = attr1.name;
                if (name != "class") {
                    attr2 = el2.attributes.getNamedItem(name);
                    if ( (attr1 === null) != (attr2 === null) ) return false;
                    if (attr1.specified != attr2.specified) return false;
                    if (attr1.specified && attr1.nodeValue !== attr2.nodeValue) return false;
                }
            }
            return true;
        }

        function elementHasNonClassAttributes(el, exceptions) {
            for (var i = 0, len = el.attributes.length, attrName; i < len; ++i) {
                attrName = el.attributes[i].name;
                if ( !(exceptions && contains(exceptions, attrName)) && el.attributes[i].specified && attrName != "class") {
                    return true;
                }
            }
            return false;
        }

        var getComputedStyleProperty = dom.getComputedStyleProperty;
        var isEditableElement = (function() {
            var testEl = document.createElement("div");
            return typeof testEl.isContentEditable == "boolean" ?
                function (node) {
                    return node && node.nodeType == 1 && node.isContentEditable;
                } :
                function (node) {
                    if (!node || node.nodeType != 1 || node.contentEditable == "false") {
                        return false;
                    }
                    return node.contentEditable == "true" || isEditableElement(node.parentNode);
                };
        })();

        function isEditingHost(node) {
            var parent;
            return node && node.nodeType == 1 &&
                (( (parent = node.parentNode) && parent.nodeType == 9 && parent.designMode == "on") ||
                (isEditableElement(node) && !isEditableElement(node.parentNode)));
        }

        function isEditable(node) {
            return (isEditableElement(node) || (node.nodeType != 1 && isEditableElement(node.parentNode))) && !isEditingHost(node);
        }

        var inlineDisplayRegex = /^inline(-block|-table)?$/i;

        function isNonInlineElement(node) {
            return node && node.nodeType == 1 && !inlineDisplayRegex.test(getComputedStyleProperty(node, "display"));
        }

        // White space characters as defined by HTML 4 (http://www.w3.org/TR/html401/struct/text.html)
        var htmlNonWhiteSpaceRegex = /[^\r\n\t\f \u200B]/;

        function isUnrenderedWhiteSpaceNode(node) {
            if (node.data.length == 0) {
                return true;
            }
            if (htmlNonWhiteSpaceRegex.test(node.data)) {
                return false;
            }
            var cssWhiteSpace = getComputedStyleProperty(node.parentNode, "whiteSpace");
            switch (cssWhiteSpace) {
                case "pre":
                case "pre-wrap":
                case "-moz-pre-wrap":
                    return false;
                case "pre-line":
                    if (/[\r\n]/.test(node.data)) {
                        return false;
                    }
            }

            // We now have a whitespace-only text node that may be rendered depending on its context. If it is adjacent to a
            // non-inline element, it will not be rendered. This seems to be a good enough definition.
            return isNonInlineElement(node.previousSibling) || isNonInlineElement(node.nextSibling);
        }

        function getRangeBoundaries(ranges) {
            var positions = [], i, range;
            for (i = 0; range = ranges[i++]; ) {
                positions.push(
                    new DomPosition(range.startContainer, range.startOffset),
                    new DomPosition(range.endContainer, range.endOffset)
                );
            }
            return positions;
        }

        function updateRangesFromBoundaries(ranges, positions) {
            for (var i = 0, range, start, end, len = ranges.length; i < len; ++i) {
                range = ranges[i];
                start = positions[i * 2];
                end = positions[i * 2 + 1];
                range.setStartAndEnd(start.node, start.offset, end.node, end.offset);
            }
        }

        function isSplitPoint(node, offset) {
            if (dom.isCharacterDataNode(node)) {
                if (offset == 0) {
                    return !!node.previousSibling;
                } else if (offset == node.length) {
                    return !!node.nextSibling;
                } else {
                    return true;
                }
            }

            return offset > 0 && offset < node.childNodes.length;
        }

        function splitNodeAt(node, descendantNode, descendantOffset, positionsToPreserve) {
            var newNode, parentNode;
            var splitAtStart = (descendantOffset == 0);

            if (dom.isAncestorOf(descendantNode, node)) {
                return node;
            }

            if (dom.isCharacterDataNode(descendantNode)) {
                var descendantIndex = dom.getNodeIndex(descendantNode);
                if (descendantOffset == 0) {
                    descendantOffset = descendantIndex;
                } else if (descendantOffset == descendantNode.length) {
                    descendantOffset = descendantIndex + 1;
                } else {
                    throw module.createError("splitNodeAt() should not be called with offset in the middle of a data node (" +
                        descendantOffset + " in " + descendantNode.data);
                }
                descendantNode = descendantNode.parentNode;
            }

            if (isSplitPoint(descendantNode, descendantOffset)) {
                // descendantNode is now guaranteed not to be a text or other character node
                newNode = descendantNode.cloneNode(false);
                parentNode = descendantNode.parentNode;
                if (newNode.id) {
                    newNode.removeAttribute("id");
                }
                var child, newChildIndex = 0;

                while ( (child = descendantNode.childNodes[descendantOffset]) ) {
                    movePreservingPositions(child, newNode, newChildIndex++, positionsToPreserve);
                }
                movePreservingPositions(newNode, parentNode, dom.getNodeIndex(descendantNode) + 1, positionsToPreserve);
                return (descendantNode == node) ? newNode : splitNodeAt(node, parentNode, dom.getNodeIndex(newNode), positionsToPreserve);
            } else if (node != descendantNode) {
                newNode = descendantNode.parentNode;

                // Work out a new split point in the parent node
                var newNodeIndex = dom.getNodeIndex(descendantNode);

                if (!splitAtStart) {
                    newNodeIndex++;
                }
                return splitNodeAt(node, newNode, newNodeIndex, positionsToPreserve);
            }
            return node;
        }

        function areElementsMergeable(el1, el2) {
            return el1.namespaceURI == el2.namespaceURI &&
                el1.tagName.toLowerCase() == el2.tagName.toLowerCase() &&
                haveSameClasses(el1, el2) &&
                elementsHaveSameNonClassAttributes(el1, el2) &&
                getComputedStyleProperty(el1, "display") == "inline" &&
                getComputedStyleProperty(el2, "display") == "inline";
        }

        function createAdjacentMergeableTextNodeGetter(forward) {
            var siblingPropName = forward ? "nextSibling" : "previousSibling";

            return function(textNode, checkParentElement) {
                var el = textNode.parentNode;
                var adjacentNode = textNode[siblingPropName];
                if (adjacentNode) {
                    // Can merge if the node's previous/next sibling is a text node
                    if (adjacentNode && adjacentNode.nodeType == 3) {
                        return adjacentNode;
                    }
                } else if (checkParentElement) {
                    // Compare text node parent element with its sibling
                    adjacentNode = el[siblingPropName];
                    if (adjacentNode && adjacentNode.nodeType == 1 && areElementsMergeable(el, adjacentNode)) {
                        var adjacentNodeChild = adjacentNode[forward ? "firstChild" : "lastChild"];
                        if (adjacentNodeChild && adjacentNodeChild.nodeType == 3) {
                            return adjacentNodeChild;
                        }
                    }
                }
                return null;
            };
        }

        var getPreviousMergeableTextNode = createAdjacentMergeableTextNodeGetter(false),
            getNextMergeableTextNode = createAdjacentMergeableTextNodeGetter(true);

    
        function Merge(firstNode) {
            this.isElementMerge = (firstNode.nodeType == 1);
            this.textNodes = [];
            var firstTextNode = this.isElementMerge ? firstNode.lastChild : firstNode;
            if (firstTextNode) {
                this.textNodes[0] = firstTextNode;
            }
        }

        Merge.prototype = {
            doMerge: function(positionsToPreserve) {
                var textNodes = this.textNodes;
                var firstTextNode = textNodes[0];
                if (textNodes.length > 1) {
                    var firstTextNodeIndex = dom.getNodeIndex(firstTextNode);
                    var textParts = [], combinedTextLength = 0, textNode, parent;
                    forEach(textNodes, function(textNode, i) {
                        parent = textNode.parentNode;
                        if (i > 0) {
                            parent.removeChild(textNode);
                            if (!parent.hasChildNodes()) {
                                dom.removeNode(parent);
                            }
                            if (positionsToPreserve) {
                                forEach(positionsToPreserve, function(position) {
                                    // Handle case where position is inside the text node being merged into a preceding node
                                    if (position.node == textNode) {
                                        position.node = firstTextNode;
                                        position.offset += combinedTextLength;
                                    }
                                    // Handle case where both text nodes precede the position within the same parent node
                                    if (position.node == parent && position.offset > firstTextNodeIndex) {
                                        --position.offset;
                                        if (position.offset == firstTextNodeIndex + 1 && i < textNodes.length - 1) {
                                            position.node = firstTextNode;
                                            position.offset = combinedTextLength;
                                        }
                                    }
                                });
                            }
                        }
                        textParts[i] = textNode.data;
                        combinedTextLength += textNode.data.length;
                    });
                    firstTextNode.data = textParts.join("");
                }
                return firstTextNode.data;
            },

            getLength: function() {
                var i = this.textNodes.length, len = 0;
                while (i--) {
                    len += this.textNodes[i].length;
                }
                return len;
            },

            toString: function() {
                var textParts = [];
                forEach(this.textNodes, function(textNode, i) {
                    textParts[i] = "'" + textNode.data + "'";
                });
                return "[Merge(" + textParts.join(",") + ")]";
            }
        };

        var optionProperties = ["elementTagName", "ignoreWhiteSpace", "applyToEditableOnly", "useExistingElements",
            "removeEmptyElements", "onElementCreate"];

        // TODO: Populate this with every attribute name that corresponds to a property with a different name. Really??
        var attrNamesForProperties = {};

        function ClassApplier(className, options, tagNames) {
            var normalize, i, len, propName, applier = this;
            applier.cssClass = applier.className = className; // cssClass property is for backward compatibility

            var elementPropertiesFromOptions = null, elementAttributes = {};

            // Initialize from options object
            if (typeof options == "object" && options !== null) {
                if (typeof options.elementTagName !== "undefined") {
                    options.elementTagName = options.elementTagName.toLowerCase();
                }
                tagNames = options.tagNames;
                elementPropertiesFromOptions = options.elementProperties;
                elementAttributes = options.elementAttributes;

                for (i = 0; propName = optionProperties[i++]; ) {
                    if (options.hasOwnProperty(propName)) {
                        applier[propName] = options[propName];
                    }
                }
                normalize = options.normalize;
            } else {
                normalize = options;
            }

            // Backward compatibility: the second parameter can also be a Boolean indicating to normalize after unapplying
            applier.normalize = (typeof normalize == "undefined") ? true : normalize;

            // Initialize element properties and attribute exceptions
            applier.attrExceptions = [];
            var el = document.createElement(applier.elementTagName);
            applier.elementProperties = applier.copyPropertiesToElement(elementPropertiesFromOptions, el, true);
            each(elementAttributes, function(attrName, attrValue) {
                applier.attrExceptions.push(attrName);
                // Ensure each attribute value is a string
                elementAttributes[attrName] = "" + attrValue;
            });
            applier.elementAttributes = elementAttributes;

            applier.elementSortedClassName = applier.elementProperties.hasOwnProperty("className") ?
                sortClassName(applier.elementProperties.className + " " + className) : className;

            // Initialize tag names
            applier.applyToAnyTagName = false;
            var type = typeof tagNames;
            if (type == "string") {
                if (tagNames == "*") {
                    applier.applyToAnyTagName = true;
                } else {
                    applier.tagNames = trim(tagNames.toLowerCase()).split(/\s*,\s*/);
                }
            } else if (type == "object" && typeof tagNames.length == "number") {
                applier.tagNames = [];
                for (i = 0, len = tagNames.length; i < len; ++i) {
                    if (tagNames[i] == "*") {
                        applier.applyToAnyTagName = true;
                    } else {
                        applier.tagNames.push(tagNames[i].toLowerCase());
                    }
                }
            } else {
                applier.tagNames = [applier.elementTagName];
            }
        }

        ClassApplier.prototype = {
            elementTagName: defaultTagName,
            elementProperties: {},
            elementAttributes: {},
            ignoreWhiteSpace: true,
            applyToEditableOnly: false,
            useExistingElements: true,
            removeEmptyElements: true,
            onElementCreate: null,

            copyPropertiesToElement: function(props, el, createCopy) {
                var s, elStyle, elProps = {}, elPropsStyle, propValue, elPropValue, attrName;

                for (var p in props) {
                    if (props.hasOwnProperty(p)) {
                        propValue = props[p];
                        elPropValue = el[p];

                        // Special case for class. The copied properties object has the applier's class as well as its own
                        // to simplify checks when removing styling elements
                        if (p == "className") {
                            addClass(el, propValue);
                            addClass(el, this.className);
                            el[p] = sortClassName(el[p]);
                            if (createCopy) {
                                elProps[p] = propValue;
                            }
                        }

                        // Special case for style
                        else if (p == "style") {
                            elStyle = elPropValue;
                            if (createCopy) {
                                elProps[p] = elPropsStyle = {};
                            }
                            for (s in props[p]) {
                                if (props[p].hasOwnProperty(s)) {
                                    elStyle[s] = propValue[s];
                                    if (createCopy) {
                                        elPropsStyle[s] = elStyle[s];
                                    }
                                }
                            }
                            this.attrExceptions.push(p);
                        } else {
                            el[p] = propValue;
                            // Copy the property back from the dummy element so that later comparisons to check whether
                            // elements may be removed are checking against the right value. For example, the href property
                            // of an element returns a fully qualified URL even if it was previously assigned a relative
                            // URL.
                            if (createCopy) {
                                elProps[p] = el[p];

                                // Not all properties map to identically-named attributes
                                attrName = attrNamesForProperties.hasOwnProperty(p) ? attrNamesForProperties[p] : p;
                                this.attrExceptions.push(attrName);
                            }
                        }
                    }
                }

                return createCopy ? elProps : "";
            },

            copyAttributesToElement: function(attrs, el) {
                for (var attrName in attrs) {
                    if (attrs.hasOwnProperty(attrName) && !/^class(?:Name)?$/i.test(attrName)) {
                        el.setAttribute(attrName, attrs[attrName]);
                    }
                }
            },

            appliesToElement: function(el) {
                return contains(this.tagNames, el.tagName.toLowerCase());
            },

            getEmptyElements: function(range) {
                var applier = this;
                return range.getNodes([1], function(el) {
                    return applier.appliesToElement(el) && !el.hasChildNodes();
                });
            },

            hasClass: function(node) {
                return node.nodeType == 1 &&
                    (this.applyToAnyTagName || this.appliesToElement(node)) &&
                    hasClass(node, this.className);
            },

            getSelfOrAncestorWithClass: function(node) {
                while (node) {
                    if (this.hasClass(node)) {
                        return node;
                    }
                    node = node.parentNode;
                }
                return null;
            },

            isModifiable: function(node) {
                return !this.applyToEditableOnly || isEditable(node);
            },

            // White space adjacent to an unwrappable node can be ignored for wrapping
            isIgnorableWhiteSpaceNode: function(node) {
                return this.ignoreWhiteSpace && node && node.nodeType == 3 && isUnrenderedWhiteSpaceNode(node);
            },

            // Normalizes nodes after applying a class to a Range.
            postApply: function(textNodes, range, positionsToPreserve, isUndo) {
                var firstNode = textNodes[0], lastNode = textNodes[textNodes.length - 1];
                var merges = [], currentMerge;
                var rangeStartNode = firstNode, rangeEndNode = lastNode;
                var rangeStartOffset = 0, rangeEndOffset = lastNode.length;
                var precedingTextNode;

                // Check for every required merge and create a Merge object for each
                forEach(textNodes, function(textNode) {
                    precedingTextNode = getPreviousMergeableTextNode(textNode, !isUndo);
                    if (precedingTextNode) {
                        if (!currentMerge) {
                            currentMerge = new Merge(precedingTextNode);
                            merges.push(currentMerge);
                        }
                        currentMerge.textNodes.push(textNode);
                        if (textNode === firstNode) {
                            rangeStartNode = currentMerge.textNodes[0];
                            rangeStartOffset = rangeStartNode.length;
                        }
                        if (textNode === lastNode) {
                            rangeEndNode = currentMerge.textNodes[0];
                            rangeEndOffset = currentMerge.getLength();
                        }
                    } else {
                        currentMerge = null;
                    }
                });

                // Test whether the first node after the range needs merging
                var nextTextNode = getNextMergeableTextNode(lastNode, !isUndo);

                if (nextTextNode) {
                    if (!currentMerge) {
                        currentMerge = new Merge(lastNode);
                        merges.push(currentMerge);
                    }
                    currentMerge.textNodes.push(nextTextNode);
                }

                // Apply the merges
                if (merges.length) {
                    for (var i = 0, len = merges.length; i < len; ++i) {
                        merges[i].doMerge(positionsToPreserve);
                    }

                    // Set the range boundaries
                    range.setStartAndEnd(rangeStartNode, rangeStartOffset, rangeEndNode, rangeEndOffset);
                }
            },

            createContainer: function(parentNode) {
                var doc = dom.getDocument(parentNode);
                var namespace;
                var el = createElementNSSupported && !dom.isHtmlNamespace(parentNode) && (namespace = parentNode.namespaceURI) ?
                    doc.createElementNS(parentNode.namespaceURI, this.elementTagName) :
                    doc.createElement(this.elementTagName);

                this.copyPropertiesToElement(this.elementProperties, el, false);
                this.copyAttributesToElement(this.elementAttributes, el);
                addClass(el, this.className);
                if (this.onElementCreate) {
                    this.onElementCreate(el, this);
                }
                return el;
            },

            elementHasProperties: function(el, props) {
                var applier = this;
                return each(props, function(p, propValue) {
                    if (p == "className") {
                        // For checking whether we should reuse an existing element, we just want to check that the element
                        // has all the classes specified in the className property. When deciding whether the element is
                        // removable when unapplying a class, there is separate special handling to check whether the
                        // element has extra classes so the same simple check will do.
                        return hasAllClasses(el, propValue);
                    } else if (typeof propValue == "object") {
                        if (!applier.elementHasProperties(el[p], propValue)) {
                            return false;
                        }
                    } else if (el[p] !== propValue) {
                        return false;
                    }
                });
            },

            elementHasAttributes: function(el, attrs) {
                return each(attrs, function(name, value) {
                    if (el.getAttribute(name) !== value) {
                        return false;
                    }
                });
            },

            applyToTextNode: function(textNode, positionsToPreserve) {

                // Check whether the text node can be styled. Text within a <style> or <script> element, for example,
                // should not be styled. See issue 283.
                if (canTextBeStyled(textNode)) {
                    var parent = textNode.parentNode;
                    if (parent.childNodes.length == 1 &&
                        this.useExistingElements &&
                        this.appliesToElement(parent) &&
                        this.elementHasProperties(parent, this.elementProperties) &&
                        this.elementHasAttributes(parent, this.elementAttributes)) {

                        addClass(parent, this.className);
                    } else {
                        var textNodeParent = textNode.parentNode;
                        var el = this.createContainer(textNodeParent);
                        textNodeParent.insertBefore(el, textNode);
                        el.appendChild(textNode);
                    }
                }

            },

            isRemovable: function(el) {
                return el.tagName.toLowerCase() == this.elementTagName &&
                    getSortedClassName(el) == this.elementSortedClassName &&
                    this.elementHasProperties(el, this.elementProperties) &&
                    !elementHasNonClassAttributes(el, this.attrExceptions) &&
                    this.elementHasAttributes(el, this.elementAttributes) &&
                    this.isModifiable(el);
            },

            isEmptyContainer: function(el) {
                var childNodeCount = el.childNodes.length;
                return el.nodeType == 1 &&
                    this.isRemovable(el) &&
                    (childNodeCount == 0 || (childNodeCount == 1 && this.isEmptyContainer(el.firstChild)));
            },

            removeEmptyContainers: function(range) {
                var applier = this;
                var nodesToRemove = range.getNodes([1], function(el) {
                    return applier.isEmptyContainer(el);
                });

                var rangesToPreserve = [range];
                var positionsToPreserve = getRangeBoundaries(rangesToPreserve);

                forEach(nodesToRemove, function(node) {
                    removePreservingPositions(node, positionsToPreserve);
                });

                // Update the range from the preserved boundary positions
                updateRangesFromBoundaries(rangesToPreserve, positionsToPreserve);
            },

            undoToTextNode: function(textNode, range, ancestorWithClass, positionsToPreserve) {
                if (!range.containsNode(ancestorWithClass)) {
                    // Split out the portion of the ancestor from which we can remove the class
                    //var parent = ancestorWithClass.parentNode, index = dom.getNodeIndex(ancestorWithClass);
                    var ancestorRange = range.cloneRange();
                    ancestorRange.selectNode(ancestorWithClass);
                    if (ancestorRange.isPointInRange(range.endContainer, range.endOffset)) {
                        splitNodeAt(ancestorWithClass, range.endContainer, range.endOffset, positionsToPreserve);
                        range.setEndAfter(ancestorWithClass);
                    }
                    if (ancestorRange.isPointInRange(range.startContainer, range.startOffset)) {
                        ancestorWithClass = splitNodeAt(ancestorWithClass, range.startContainer, range.startOffset, positionsToPreserve);
                    }
                }

                if (this.isRemovable(ancestorWithClass)) {
                    replaceWithOwnChildrenPreservingPositions(ancestorWithClass, positionsToPreserve);
                } else {
                    removeClass(ancestorWithClass, this.className);
                }
            },

            splitAncestorWithClass: function(container, offset, positionsToPreserve) {
                var ancestorWithClass = this.getSelfOrAncestorWithClass(container);
                if (ancestorWithClass) {
                    splitNodeAt(ancestorWithClass, container, offset, positionsToPreserve);
                }
            },

            undoToAncestor: function(ancestorWithClass, positionsToPreserve) {
                if (this.isRemovable(ancestorWithClass)) {
                    replaceWithOwnChildrenPreservingPositions(ancestorWithClass, positionsToPreserve);
                } else {
                    removeClass(ancestorWithClass, this.className);
                }
            },

            applyToRange: function(range, rangesToPreserve) {
                var applier = this;
                rangesToPreserve = rangesToPreserve || [];

                // Create an array of range boundaries to preserve
                var positionsToPreserve = getRangeBoundaries(rangesToPreserve || []);

                range.splitBoundariesPreservingPositions(positionsToPreserve);

                // Tidy up the DOM by removing empty containers
                if (applier.removeEmptyElements) {
                    applier.removeEmptyContainers(range);
                }

                var textNodes = getEffectiveTextNodes(range);

                if (textNodes.length) {
                    forEach(textNodes, function(textNode) {
                        if (!applier.isIgnorableWhiteSpaceNode(textNode) && !applier.getSelfOrAncestorWithClass(textNode) &&
                                applier.isModifiable(textNode)) {
                            applier.applyToTextNode(textNode, positionsToPreserve);
                        }
                    });
                    var lastTextNode = textNodes[textNodes.length - 1];
                    range.setStartAndEnd(textNodes[0], 0, lastTextNode, lastTextNode.length);
                    if (applier.normalize) {
                        applier.postApply(textNodes, range, positionsToPreserve, false);
                    }

                    // Update the ranges from the preserved boundary positions
                    updateRangesFromBoundaries(rangesToPreserve, positionsToPreserve);
                }

                // Apply classes to any appropriate empty elements
                var emptyElements = applier.getEmptyElements(range);

                forEach(emptyElements, function(el) {
                    addClass(el, applier.className);
                });
            },

            applyToRanges: function(ranges) {

                var i = ranges.length;
                while (i--) {
                    this.applyToRange(ranges[i], ranges);
                }


                return ranges;
            },

            applyToSelection: function(win) {
                var sel = api.getSelection(win);
                sel.setRanges( this.applyToRanges(sel.getAllRanges()) );
            },

            undoToRange: function(range, rangesToPreserve) {
                var applier = this;
                // Create an array of range boundaries to preserve
                rangesToPreserve = rangesToPreserve || [];
                var positionsToPreserve = getRangeBoundaries(rangesToPreserve);


                range.splitBoundariesPreservingPositions(positionsToPreserve);

                // Tidy up the DOM by removing empty containers
                if (applier.removeEmptyElements) {
                    applier.removeEmptyContainers(range, positionsToPreserve);
                }

                var textNodes = getEffectiveTextNodes(range);
                var textNode, ancestorWithClass;
                var lastTextNode = textNodes[textNodes.length - 1];

                if (textNodes.length) {
                    applier.splitAncestorWithClass(range.endContainer, range.endOffset, positionsToPreserve);
                    applier.splitAncestorWithClass(range.startContainer, range.startOffset, positionsToPreserve);
                    for (var i = 0, len = textNodes.length; i < len; ++i) {
                        textNode = textNodes[i];
                        ancestorWithClass = applier.getSelfOrAncestorWithClass(textNode);
                        if (ancestorWithClass && applier.isModifiable(textNode)) {
                            applier.undoToAncestor(ancestorWithClass, positionsToPreserve);
                        }
                    }
                    // Ensure the range is still valid
                    range.setStartAndEnd(textNodes[0], 0, lastTextNode, lastTextNode.length);


                    if (applier.normalize) {
                        applier.postApply(textNodes, range, positionsToPreserve, true);
                    }

                    // Update the ranges from the preserved boundary positions
                    updateRangesFromBoundaries(rangesToPreserve, positionsToPreserve);
                }

                // Remove class from any appropriate empty elements
                var emptyElements = applier.getEmptyElements(range);

                forEach(emptyElements, function(el) {
                    removeClass(el, applier.className);
                });
            },

            undoToRanges: function(ranges) {
                // Get ranges returned in document order
                var i = ranges.length;

                while (i--) {
                    this.undoToRange(ranges[i], ranges);
                }

                return ranges;
            },

            undoToSelection: function(win) {
                var sel = api.getSelection(win);
                var ranges = api.getSelection(win).getAllRanges();
                this.undoToRanges(ranges);
                sel.setRanges(ranges);
            },

            isAppliedToRange: function(range) {
                if (range.collapsed || range.toString() == "") {
                    return !!this.getSelfOrAncestorWithClass(range.commonAncestorContainer);
                } else {
                    var textNodes = range.getNodes( [3] );
                    if (textNodes.length)
                    for (var i = 0, textNode; textNode = textNodes[i++]; ) {
                        if (!this.isIgnorableWhiteSpaceNode(textNode) && rangeSelectsAnyText(range, textNode) &&
                                this.isModifiable(textNode) && !this.getSelfOrAncestorWithClass(textNode)) {
                            return false;
                        }
                    }
                    return true;
                }
            },

            isAppliedToRanges: function(ranges) {
                var i = ranges.length;
                if (i == 0) {
                    return false;
                }
                while (i--) {
                    if (!this.isAppliedToRange(ranges[i])) {
                        return false;
                    }
                }
                return true;
            },

            isAppliedToSelection: function(win) {
                var sel = api.getSelection(win);
                return this.isAppliedToRanges(sel.getAllRanges());
            },

            toggleRange: function(range) {
                if (this.isAppliedToRange(range)) {
                    this.undoToRange(range);
                } else {
                    this.applyToRange(range);
                }
            },

            toggleSelection: function(win) {
                if (this.isAppliedToSelection(win)) {
                    this.undoToSelection(win);
                } else {
                    this.applyToSelection(win);
                }
            },

            getElementsWithClassIntersectingRange: function(range) {
                var elements = [];
                var applier = this;
                range.getNodes([3], function(textNode) {
                    var el = applier.getSelfOrAncestorWithClass(textNode);
                    if (el && !contains(elements, el)) {
                        elements.push(el);
                    }
                });
                return elements;
            },

            detach: function() {}
        };

        function createClassApplier(className, options, tagNames) {
            return new ClassApplier(className, options, tagNames);
        }

        ClassApplier.util = {
            hasClass: hasClass,
            addClass: addClass,
            removeClass: removeClass,
            getClass: getClass,
            hasSameClasses: haveSameClasses,
            hasAllClasses: hasAllClasses,
            replaceWithOwnChildren: replaceWithOwnChildrenPreservingPositions,
            elementsHaveSameNonClassAttributes: elementsHaveSameNonClassAttributes,
            elementHasNonClassAttributes: elementHasNonClassAttributes,
            splitNodeAt: splitNodeAt,
            isEditableElement: isEditableElement,
            isEditingHost: isEditingHost,
            isEditable: isEditable
        };

        api.CssClassApplier = api.ClassApplier = ClassApplier;
        api.createClassApplier = createClassApplier;
        util.createAliasForDeprecatedMethod(api, "createCssClassApplier", "createClassApplier", module);
    });
    
    return rangy;
}, this);

/**
 * Highlighter module for Rangy, a cross-browser JavaScript range and selection library
 * https://github.com/timdown/rangy
 *
 * Depends on Rangy core, ClassApplier and optionally TextRange modules.
 *
 * Copyright 2024, Tim Down
 * Licensed under the MIT license.
 * Version: 1.3.2
 * Build date: 2 November 2024
 */
(function(factory, root) {
    if (typeof define == "function" && define.amd) {
        // AMD. Register as an anonymous module with a dependency on Rangy.
        define(["./rangy-core"], factory);
    } else if (typeof module != "undefined" && typeof exports == "object") {
        // Node/CommonJS style
        module.exports = factory( require("rangy") );
    } else {
        // No AMD or CommonJS support so we use the rangy property of root (probably the global variable)
        factory(root.rangy);
    }
})(function(rangy) {
    rangy.createModule("Highlighter", ["ClassApplier"], function(api, module) {
        var dom = api.dom;
        var contains = dom.arrayContains;
        var getBody = dom.getBody;
        var createOptions = api.util.createOptions;
        var forEach = api.util.forEach;
        var nextHighlightId = 1;

        // Puts highlights in order, last in document first.
        function compareHighlights(h1, h2) {
            return h1.characterRange.start - h2.characterRange.start;
        }

        function getContainerElement(doc, id) {
            return id ? doc.getElementById(id) : getBody(doc);
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        var highlighterTypes = {};

        function HighlighterType(type, converterCreator) {
            this.type = type;
            this.converterCreator = converterCreator;
        }

        HighlighterType.prototype.create = function() {
            var converter = this.converterCreator();
            converter.type = this.type;
            return converter;
        };

        function registerHighlighterType(type, converterCreator) {
            highlighterTypes[type] = new HighlighterType(type, converterCreator);
        }

        function getConverter(type) {
            var highlighterType = highlighterTypes[type];
            if (highlighterType instanceof HighlighterType) {
                return highlighterType.create();
            } else {
                throw new Error("Highlighter type '" + type + "' is not valid");
            }
        }

        api.registerHighlighterType = registerHighlighterType;

        /*----------------------------------------------------------------------------------------------------------------*/

        function CharacterRange(start, end) {
            this.start = start;
            this.end = end;
        }

        CharacterRange.prototype = {
            intersects: function(charRange) {
                return this.start < charRange.end && this.end > charRange.start;
            },

            isContiguousWith: function(charRange) {
                return this.start == charRange.end || this.end == charRange.start;
            },

            union: function(charRange) {
                return new CharacterRange(Math.min(this.start, charRange.start), Math.max(this.end, charRange.end));
            },

            intersection: function(charRange) {
                return new CharacterRange(Math.max(this.start, charRange.start), Math.min(this.end, charRange.end));
            },

            getComplements: function(charRange) {
                var ranges = [];
                if (this.start >= charRange.start) {
                    if (this.end <= charRange.end) {
                        return [];
                    }
                    ranges.push(new CharacterRange(charRange.end, this.end));
                } else {
                    ranges.push(new CharacterRange(this.start, Math.min(this.end, charRange.start)));
                    if (this.end > charRange.end) {
                        ranges.push(new CharacterRange(charRange.end, this.end));
                    }
                }
                return ranges;
            },

            toString: function() {
                return "[CharacterRange(" + this.start + ", " + this.end + ")]";
            }
        };

        CharacterRange.fromCharacterRange = function(charRange) {
            return new CharacterRange(charRange.start, charRange.end);
        };

        /*----------------------------------------------------------------------------------------------------------------*/

        var textContentConverter = {
            rangeToCharacterRange: function(range, containerNode) {
                var bookmark = range.getBookmark(containerNode);
                return new CharacterRange(bookmark.start, bookmark.end);
            },

            characterRangeToRange: function(doc, characterRange, containerNode) {
                var range = api.createRange(doc);
                range.moveToBookmark({
                    start: characterRange.start,
                    end: characterRange.end,
                    containerNode: containerNode
                });

                return range;
            },

            serializeSelection: function(selection, containerNode) {
                var ranges = selection.getAllRanges(), rangeCount = ranges.length;
                var rangeInfos = [];

                var backward = rangeCount == 1 && selection.isBackward();

                for (var i = 0, len = ranges.length; i < len; ++i) {
                    rangeInfos[i] = {
                        characterRange: this.rangeToCharacterRange(ranges[i], containerNode),
                        backward: backward
                    };
                }

                return rangeInfos;
            },

            restoreSelection: function(selection, savedSelection, containerNode) {
                selection.removeAllRanges();
                var doc = selection.win.document;
                for (var i = 0, len = savedSelection.length, range, rangeInfo, characterRange; i < len; ++i) {
                    rangeInfo = savedSelection[i];
                    characterRange = rangeInfo.characterRange;
                    range = this.characterRangeToRange(doc, rangeInfo.characterRange, containerNode);
                    selection.addRange(range, rangeInfo.backward);
                }
            }
        };

        registerHighlighterType("textContent", function() {
            return textContentConverter;
        });

        /*----------------------------------------------------------------------------------------------------------------*/

        // Lazily load the TextRange-based converter so that the dependency is only checked when required.
        registerHighlighterType("TextRange", (function() {
            var converter;

            return function() {
                if (!converter) {
                    // Test that textRangeModule exists and is supported
                    var textRangeModule = api.modules.TextRange;
                    if (!textRangeModule) {
                        throw new Error("TextRange module is missing.");
                    } else if (!textRangeModule.supported) {
                        throw new Error("TextRange module is present but not supported.");
                    }

                    converter = {
                        rangeToCharacterRange: function(range, containerNode) {
                            return CharacterRange.fromCharacterRange( range.toCharacterRange(containerNode) );
                        },

                        characterRangeToRange: function(doc, characterRange, containerNode) {
                            var range = api.createRange(doc);
                            range.selectCharacters(containerNode, characterRange.start, characterRange.end);
                            return range;
                        },

                        serializeSelection: function(selection, containerNode) {
                            return selection.saveCharacterRanges(containerNode);
                        },

                        restoreSelection: function(selection, savedSelection, containerNode) {
                            selection.restoreCharacterRanges(containerNode, savedSelection);
                        }
                    };
                }

                return converter;
            };
        })());

        /*----------------------------------------------------------------------------------------------------------------*/

        function Highlight(doc, characterRange, classApplier, converter, id, containerElementId) {
            if (id) {
                this.id = id;
                nextHighlightId = Math.max(nextHighlightId, id + 1);
            } else {
                this.id = nextHighlightId++;
            }
            this.characterRange = characterRange;
            this.doc = doc;
            this.classApplier = classApplier;
            this.converter = converter;
            this.containerElementId = containerElementId || null;
            this.applied = false;
        }

        Highlight.prototype = {
            getContainerElement: function() {
                return getContainerElement(this.doc, this.containerElementId);
            },

            getRange: function() {
                return this.converter.characterRangeToRange(this.doc, this.characterRange, this.getContainerElement());
            },

            fromRange: function(range) {
                this.characterRange = this.converter.rangeToCharacterRange(range, this.getContainerElement());
            },

            getText: function() {
                return this.getRange().toString();
            },

            containsElement: function(el) {
                return this.getRange().containsNodeContents(el.firstChild);
            },

            unapply: function() {
                this.classApplier.undoToRange(this.getRange());
                this.applied = false;
            },

            apply: function() {
                this.classApplier.applyToRange(this.getRange());
                this.applied = true;
            },

            getHighlightElements: function() {
                return this.classApplier.getElementsWithClassIntersectingRange(this.getRange());
            },

            toString: function() {
                return "[Highlight(ID: " + this.id + ", class: " + this.classApplier.className + ", character range: " +
                    this.characterRange.start + " - " + this.characterRange.end + ")]";
            }
        };

        /*----------------------------------------------------------------------------------------------------------------*/

        function Highlighter(doc, type) {
            type = type || "textContent";
            this.doc = doc || document;
            this.classAppliers = {};
            this.highlights = [];
            this.converter = getConverter(type);
        }

        Highlighter.prototype = {
            addClassApplier: function(classApplier) {
                this.classAppliers[classApplier.className] = classApplier;
            },

            getHighlightForElement: function(el) {
                var highlights = this.highlights;
                for (var i = 0, len = highlights.length; i < len; ++i) {
                    if (highlights[i].containsElement(el)) {
                        return highlights[i];
                    }
                }
                return null;
            },

            removeHighlights: function(highlights) {
                for (var i = 0, len = this.highlights.length, highlight; i < len; ++i) {
                    highlight = this.highlights[i];
                    if (contains(highlights, highlight)) {
                        highlight.unapply();
                        this.highlights.splice(i--, 1);
                    }
                }
            },

            removeAllHighlights: function() {
                this.removeHighlights(this.highlights);
            },

            getIntersectingHighlights: function(ranges) {
                // Test each range against each of the highlighted ranges to see whether they overlap
                var intersectingHighlights = [], highlights = this.highlights;
                forEach(ranges, function(range) {
                    //var selCharRange = converter.rangeToCharacterRange(range);
                    forEach(highlights, function(highlight) {
                        if (range.intersectsRange( highlight.getRange() ) && !contains(intersectingHighlights, highlight)) {
                            intersectingHighlights.push(highlight);
                        }
                    });
                });

                return intersectingHighlights;
            },

            highlightCharacterRanges: function(className, charRanges, options) {
                var i, len, j;
                var highlights = this.highlights;
                var converter = this.converter;
                var doc = this.doc;
                var highlightsToRemove = [];
                var classApplier = className ? this.classAppliers[className] : null;

                options = createOptions(options, {
                    containerElementId: null,
                    exclusive: true
                });

                var containerElementId = options.containerElementId;
                var exclusive = options.exclusive;

                var containerElement, containerElementRange, containerElementCharRange;
                if (containerElementId) {
                    containerElement = this.doc.getElementById(containerElementId);
                    if (containerElement) {
                        containerElementRange = api.createRange(this.doc);
                        containerElementRange.selectNodeContents(containerElement);
                        containerElementCharRange = new CharacterRange(0, containerElementRange.toString().length);
                    }
                }

                var charRange, highlightCharRange, removeHighlight, isSameClassApplier, highlightsToKeep, splitHighlight;

                for (i = 0, len = charRanges.length; i < len; ++i) {
                    charRange = charRanges[i];
                    highlightsToKeep = [];

                    // Restrict character range to container element, if it exists
                    if (containerElementCharRange) {
                        charRange = charRange.intersection(containerElementCharRange);
                    }

                    // Ignore empty ranges
                    if (charRange.start == charRange.end) {
                        continue;
                    }

                    // Check for intersection with existing highlights. For each intersection, create a new highlight
                    // which is the union of the highlight range and the selected range
                    for (j = 0; j < highlights.length; ++j) {
                        removeHighlight = false;

                        if (containerElementId == highlights[j].containerElementId) {
                            highlightCharRange = highlights[j].characterRange;
                            isSameClassApplier = (classApplier == highlights[j].classApplier);
                            splitHighlight = !isSameClassApplier && exclusive;

                            // Replace the existing highlight if it needs to be:
                            //  1. merged (isSameClassApplier)
                            //  2. partially or entirely erased (className === null)
                            //  3. partially or entirely replaced (isSameClassApplier == false && exclusive == true)
                            if (    (highlightCharRange.intersects(charRange) || highlightCharRange.isContiguousWith(charRange)) &&
                                    (isSameClassApplier || splitHighlight) ) {

                                // Remove existing highlights, keeping the unselected parts
                                if (splitHighlight) {
                                    forEach(highlightCharRange.getComplements(charRange), function(rangeToAdd) {
                                        highlightsToKeep.push( new Highlight(doc, rangeToAdd, highlights[j].classApplier, converter, null, containerElementId) );
                                    });
                                }

                                removeHighlight = true;
                                if (isSameClassApplier) {
                                    charRange = highlightCharRange.union(charRange);
                                }
                            }
                        }

                        if (removeHighlight) {
                            highlightsToRemove.push(highlights[j]);
                            highlights[j] = new Highlight(doc, highlightCharRange.union(charRange), classApplier, converter, null, containerElementId);
                        } else {
                            highlightsToKeep.push(highlights[j]);
                        }
                    }

                    // Add new range
                    if (classApplier) {
                        highlightsToKeep.push(new Highlight(doc, charRange, classApplier, converter, null, containerElementId));
                    }
                    this.highlights = highlights = highlightsToKeep;
                }

                // Remove the old highlights
                forEach(highlightsToRemove, function(highlightToRemove) {
                    highlightToRemove.unapply();
                });

                // Apply new highlights
                var newHighlights = [];
                forEach(highlights, function(highlight) {
                    if (!highlight.applied) {
                        highlight.apply();
                        newHighlights.push(highlight);
                    }
                });

                return newHighlights;
            },

            highlightRanges: function(className, ranges, options) {
                var selCharRanges = [];
                var converter = this.converter;

                options = createOptions(options, {
                    containerElement: null,
                    exclusive: true
                });

                var containerElement = options.containerElement;
                var containerElementId = containerElement ? containerElement.id : null;
                var containerElementRange;
                if (containerElement) {
                    containerElementRange = api.createRange(containerElement);
                    containerElementRange.selectNodeContents(containerElement);
                }

                forEach(ranges, function(range) {
                    var scopedRange = containerElement ? containerElementRange.intersection(range) : range;
                    selCharRanges.push( converter.rangeToCharacterRange(scopedRange, containerElement || getBody(range.getDocument())) );
                });

                return this.highlightCharacterRanges(className, selCharRanges, {
                    containerElementId: containerElementId,
                    exclusive: options.exclusive
                });
            },

            highlightSelection: function(className, options) {
                var converter = this.converter;
                var classApplier = className ? this.classAppliers[className] : false;

                options = createOptions(options, {
                    containerElementId: null,
                    exclusive: true
                });

                var containerElementId = options.containerElementId;
                var exclusive = options.exclusive;
                var selection = options.selection || api.getSelection(this.doc);
                var doc = selection.win.document;
                var containerElement = getContainerElement(doc, containerElementId);

                if (!classApplier && className !== false) {
                    throw new Error("No class applier found for class '" + className + "'");
                }

                // Store the existing selection as character ranges
                var serializedSelection = converter.serializeSelection(selection, containerElement);

                // Create an array of selected character ranges
                var selCharRanges = [];
                forEach(serializedSelection, function(rangeInfo) {
                    selCharRanges.push( CharacterRange.fromCharacterRange(rangeInfo.characterRange) );
                });

                var newHighlights = this.highlightCharacterRanges(className, selCharRanges, {
                    containerElementId: containerElementId,
                    exclusive: exclusive
                });

                // Restore selection
                converter.restoreSelection(selection, serializedSelection, containerElement);

                return newHighlights;
            },

            unhighlightSelection: function(selection) {
                selection = selection || api.getSelection(this.doc);
                var intersectingHighlights = this.getIntersectingHighlights( selection.getAllRanges() );
                this.removeHighlights(intersectingHighlights);
                selection.removeAllRanges();
                return intersectingHighlights;
            },

            getHighlightsInSelection: function(selection) {
                selection = selection || api.getSelection(this.doc);
                return this.getIntersectingHighlights(selection.getAllRanges());
            },

            selectionOverlapsHighlight: function(selection) {
                return this.getHighlightsInSelection(selection).length > 0;
            },

            serialize: function(options) {
                var highlighter = this;
                var highlights = highlighter.highlights;
                var serializedType, serializedHighlights, convertType, serializationConverter;

                highlights.sort(compareHighlights);
                options = createOptions(options, {
                    serializeHighlightText: false,
                    type: highlighter.converter.type
                });

                serializedType = options.type;
                convertType = (serializedType != highlighter.converter.type);

                if (convertType) {
                    serializationConverter = getConverter(serializedType);
                }

                serializedHighlights = ["type:" + serializedType];

                forEach(highlights, function(highlight) {
                    var characterRange = highlight.characterRange;
                    var containerElement;

                    // Convert to the current Highlighter's type, if different from the serialization type
                    if (convertType) {
                        containerElement = highlight.getContainerElement();
                        characterRange = serializationConverter.rangeToCharacterRange(
                            highlighter.converter.characterRangeToRange(highlighter.doc, characterRange, containerElement),
                            containerElement
                        );
                    }

                    var parts = [
                        characterRange.start,
                        characterRange.end,
                        highlight.id,
                        highlight.classApplier.className,
                        highlight.containerElementId
                    ];

                    if (options.serializeHighlightText) {
                        parts.push(highlight.getText());
                    }
                    serializedHighlights.push( parts.join("$") );
                });

                return serializedHighlights.join("|");
            },

            deserialize: function(serialized) {
                var serializedHighlights = serialized.split("|");
                var highlights = [];

                var firstHighlight = serializedHighlights[0];
                var regexResult;
                var serializationType, serializationConverter, convertType = false;
                if ( firstHighlight && (regexResult = /^type:(\w+)$/.exec(firstHighlight)) ) {
                    serializationType = regexResult[1];
                    if (serializationType != this.converter.type) {
                        serializationConverter = getConverter(serializationType);
                        convertType = true;
                    }
                    serializedHighlights.shift();
                } else {
                    throw new Error("Serialized highlights are invalid.");
                }

                var classApplier, highlight, characterRange, containerElementId, containerElement;

                for (var i = serializedHighlights.length, parts; i-- > 0; ) {
                    parts = serializedHighlights[i].split("$");
                    characterRange = new CharacterRange(+parts[0], +parts[1]);
                    containerElementId = parts[4] || null;

                    // Convert to the current Highlighter's type, if different from the serialization type
                    if (convertType) {
                        containerElement = getContainerElement(this.doc, containerElementId);
                        characterRange = this.converter.rangeToCharacterRange(
                            serializationConverter.characterRangeToRange(this.doc, characterRange, containerElement),
                            containerElement
                        );
                    }

                    classApplier = this.classAppliers[ parts[3] ];

                    if (!classApplier) {
                        throw new Error("No class applier found for class '" + parts[3] + "'");
                    }

                    highlight = new Highlight(this.doc, characterRange, classApplier, this.converter, parseInt(parts[2]), containerElementId);
                    highlight.apply();
                    highlights.push(highlight);
                }
                this.highlights = highlights;
            }
        };

        api.Highlighter = Highlighter;

        api.createHighlighter = function(doc, rangeCharacterOffsetConverterType) {
            return new Highlighter(doc, rangeCharacterOffsetConverterType);
        };
    });
    
    return rangy;
}, this);

/**
 * Selection save and restore module for Rangy.
 * Saves and restores user selections using marker invisible elements in the DOM.
 *
 * Part of Rangy, a cross-browser JavaScript range and selection library
 * https://github.com/timdown/rangy
 *
 * Depends on Rangy core.
 *
 * Copyright 2024, Tim Down
 * Licensed under the MIT license.
 * Version: 1.3.2
 * Build date: 2 November 2024
 */
(function(factory, root) {
    if (typeof define == "function" && define.amd) {
        // AMD. Register as an anonymous module with a dependency on Rangy.
        define(["./rangy-core"], factory);
    } else if (typeof module != "undefined" && typeof exports == "object") {
        // Node/CommonJS style
        module.exports = factory( require("rangy") );
    } else {
        // No AMD or CommonJS support so we use the rangy property of root (probably the global variable)
        factory(root.rangy);
    }
})(function(rangy) {
    rangy.createModule("SaveRestore", ["WrappedSelection"], function(api, module) {
        var dom = api.dom;
        var removeNode = dom.removeNode;
        var isDirectionBackward = api.Selection.isDirectionBackward;
        var markerTextChar = "\ufeff";

        function gEBI(id, doc) {
            return (doc || document).getElementById(id);
        }

        function insertRangeBoundaryMarker(range, atStart) {
            var markerId = "selectionBoundary_" + (+new Date()) + "_" + ("" + Math.random()).slice(2);
            var markerEl;
            var doc = dom.getDocument(range.startContainer);

            // Clone the Range and collapse to the appropriate boundary point
            var boundaryRange = range.cloneRange();
            boundaryRange.collapse(atStart);

            // Create the marker element containing a single invisible character using DOM methods and insert it
            markerEl = doc.createElement("span");
            markerEl.id = markerId;
            markerEl.style.lineHeight = "0";
            markerEl.style.display = "none";
            markerEl.className = "rangySelectionBoundary";
            markerEl.appendChild(doc.createTextNode(markerTextChar));

            boundaryRange.insertNode(markerEl);
            return markerEl;
        }

        function setRangeBoundary(doc, range, markerId, atStart) {
            var markerEl = gEBI(markerId, doc);
            if (markerEl) {
                range[atStart ? "setStartBefore" : "setEndBefore"](markerEl);
                removeNode(markerEl);
            } else {
                module.warn("Marker element has been removed. Cannot restore selection.");
            }
        }

        function compareRanges(r1, r2) {
            return r2.compareBoundaryPoints(r1.START_TO_START, r1);
        }

        function saveRange(range, direction) {
            var startEl, endEl, doc = api.DomRange.getRangeDocument(range), text = range.toString();
            var backward = isDirectionBackward(direction);

            if (range.collapsed) {
                endEl = insertRangeBoundaryMarker(range, false);
                return {
                    document: doc,
                    markerId: endEl.id,
                    collapsed: true
                };
            } else {
                endEl = insertRangeBoundaryMarker(range, false);
                startEl = insertRangeBoundaryMarker(range, true);

                return {
                    document: doc,
                    startMarkerId: startEl.id,
                    endMarkerId: endEl.id,
                    collapsed: false,
                    backward: backward,
                    toString: function() {
                        return "original text: '" + text + "', new text: '" + range.toString() + "'";
                    }
                };
            }
        }

        function restoreRange(rangeInfo, normalize) {
            var doc = rangeInfo.document;
            if (typeof normalize == "undefined") {
                normalize = true;
            }
            var range = api.createRange(doc);
            if (rangeInfo.collapsed) {
                var markerEl = gEBI(rangeInfo.markerId, doc);
                if (markerEl) {
                    markerEl.style.display = "inline";
                    var previousNode = markerEl.previousSibling;

                    // Workaround for issue 17
                    if (previousNode && previousNode.nodeType == 3) {
                        removeNode(markerEl);
                        range.collapseToPoint(previousNode, previousNode.length);
                    } else {
                        range.collapseBefore(markerEl);
                        removeNode(markerEl);
                    }
                } else {
                    module.warn("Marker element has been removed. Cannot restore selection.");
                }
            } else {
                setRangeBoundary(doc, range, rangeInfo.startMarkerId, true);
                setRangeBoundary(doc, range, rangeInfo.endMarkerId, false);
            }

            if (normalize) {
                range.normalizeBoundaries();
            }

            return range;
        }

        function saveRanges(ranges, direction) {
            var rangeInfos = [], range, doc;
            var backward = isDirectionBackward(direction);

            // Order the ranges by position within the DOM, latest first, cloning the array to leave the original untouched
            ranges = ranges.slice(0);
            ranges.sort(compareRanges);

            for (var i = 0, len = ranges.length; i < len; ++i) {
                rangeInfos[i] = saveRange(ranges[i], backward);
            }

            // Now that all the markers are in place and DOM manipulation over, adjust each range's boundaries to lie
            // between its markers
            for (i = len - 1; i >= 0; --i) {
                range = ranges[i];
                doc = api.DomRange.getRangeDocument(range);
                if (range.collapsed) {
                    range.collapseAfter(gEBI(rangeInfos[i].markerId, doc));
                } else {
                    range.setEndBefore(gEBI(rangeInfos[i].endMarkerId, doc));
                    range.setStartAfter(gEBI(rangeInfos[i].startMarkerId, doc));
                }
            }

            return rangeInfos;
        }

        function saveSelection(win) {
            if (!api.isSelectionValid(win)) {
                module.warn("Cannot save selection. This usually happens when the selection is collapsed and the selection document has lost focus.");
                return null;
            }
            var sel = api.getSelection(win);
            var ranges = sel.getAllRanges();
            var backward = (ranges.length == 1 && sel.isBackward());

            var rangeInfos = saveRanges(ranges, backward);

            // Ensure current selection is unaffected
            if (backward) {
                sel.setSingleRange(ranges[0], backward);
            } else {
                sel.setRanges(ranges);
            }

            return {
                win: win,
                rangeInfos: rangeInfos,
                restored: false
            };
        }

        function restoreRanges(rangeInfos) {
            var ranges = [];

            // Ranges are in reverse order of appearance in the DOM. We want to restore earliest first to avoid
            // normalization affecting previously restored ranges.
            var rangeCount = rangeInfos.length;

            for (var i = rangeCount - 1; i >= 0; i--) {
                ranges[i] = restoreRange(rangeInfos[i], true);
            }

            return ranges;
        }

        function restoreSelection(savedSelection, preserveDirection) {
            if (!savedSelection.restored) {
                var rangeInfos = savedSelection.rangeInfos;
                var sel = api.getSelection(savedSelection.win);
                var ranges = restoreRanges(rangeInfos), rangeCount = rangeInfos.length;

                if (rangeCount == 1 && preserveDirection && api.features.selectionHasExtend && rangeInfos[0].backward) {
                    sel.removeAllRanges();
                    sel.addRange(ranges[0], true);
                } else {
                    sel.setRanges(ranges);
                }

                savedSelection.restored = true;
            }
        }

        function removeMarkerElement(doc, markerId) {
            var markerEl = gEBI(markerId, doc);
            if (markerEl) {
                removeNode(markerEl);
            }
        }

        function removeMarkers(savedSelection) {
            var rangeInfos = savedSelection.rangeInfos;
            for (var i = 0, len = rangeInfos.length, rangeInfo; i < len; ++i) {
                rangeInfo = rangeInfos[i];
                if (rangeInfo.collapsed) {
                    removeMarkerElement(savedSelection.doc, rangeInfo.markerId);
                } else {
                    removeMarkerElement(savedSelection.doc, rangeInfo.startMarkerId);
                    removeMarkerElement(savedSelection.doc, rangeInfo.endMarkerId);
                }
            }
        }

        api.util.extend(api, {
            saveRange: saveRange,
            restoreRange: restoreRange,
            saveRanges: saveRanges,
            restoreRanges: restoreRanges,
            saveSelection: saveSelection,
            restoreSelection: restoreSelection,
            removeMarkerElement: removeMarkerElement,
            removeMarkers: removeMarkers
        });
    });
    
    return rangy;
}, this);
/**
 * Serializer module for Rangy.
 * Serializes Ranges and Selections. An example use would be to store a user's selection on a particular page in a
 * cookie or local storage and restore it on the user's next visit to the same page.
 *
 * Part of Rangy, a cross-browser JavaScript range and selection library
 * https://github.com/timdown/rangy
 *
 * Depends on Rangy core.
 *
 * Copyright 2024, Tim Down
 * Licensed under the MIT license.
 * Version: 1.3.2
 * Build date: 2 November 2024
 */
(function(factory, root) {
    if (typeof define == "function" && define.amd) {
        // AMD. Register as an anonymous module with a dependency on Rangy.
        define(["./rangy-core"], factory);
    } else if (typeof module != "undefined" && typeof exports == "object") {
        // Node/CommonJS style
        module.exports = factory( require("rangy") );
    } else {
        // No AMD or CommonJS support so we use the rangy property of root (probably the global variable)
        factory(root.rangy);
    }
})(function(rangy) {
    rangy.createModule("Serializer", ["WrappedSelection"], function(api, module) {
        var UNDEF = "undefined";
        var util = api.util;

        // encodeURIComponent and decodeURIComponent are required for cookie handling
        if (typeof encodeURIComponent == UNDEF || typeof decodeURIComponent == UNDEF) {
            module.fail("encodeURIComponent and/or decodeURIComponent method is missing");
        }

        // Checksum for checking whether range can be serialized
        var crc32 = (function() {
            function utf8encode(str) {
                var utf8CharCodes = [];

                for (var i = 0, len = str.length, c; i < len; ++i) {
                    c = str.charCodeAt(i);
                    if (c < 128) {
                        utf8CharCodes.push(c);
                    } else if (c < 2048) {
                        utf8CharCodes.push((c >> 6) | 192, (c & 63) | 128);
                    } else {
                        utf8CharCodes.push((c >> 12) | 224, ((c >> 6) & 63) | 128, (c & 63) | 128);
                    }
                }
                return utf8CharCodes;
            }

            var cachedCrcTable = null;

            function buildCRCTable() {
                var table = [];
                for (var i = 0, j, crc; i < 256; ++i) {
                    crc = i;
                    j = 8;
                    while (j--) {
                        if ((crc & 1) == 1) {
                            crc = (crc >>> 1) ^ 0xEDB88320;
                        } else {
                            crc >>>= 1;
                        }
                    }
                    table[i] = crc >>> 0;
                }
                return table;
            }

            function getCrcTable() {
                if (!cachedCrcTable) {
                    cachedCrcTable = buildCRCTable();
                }
                return cachedCrcTable;
            }

            return function(str) {
                var utf8CharCodes = utf8encode(str), crc = -1, crcTable = getCrcTable();
                for (var i = 0, len = utf8CharCodes.length, y; i < len; ++i) {
                    y = (crc ^ utf8CharCodes[i]) & 0xFF;
                    crc = (crc >>> 8) ^ crcTable[y];
                }
                return (crc ^ -1) >>> 0;
            };
        })();

        var dom = api.dom;

        function escapeTextForHtml(str) {
            return str.replace(/</g, "&lt;").replace(/>/g, "&gt;");
        }

        function nodeToInfoString(node, infoParts) {
            infoParts = infoParts || [];
            var nodeType = node.nodeType, children = node.childNodes, childCount = children.length;
            var nodeInfo = [nodeType, node.nodeName, childCount].join(":");
            var start = "", end = "";
            switch (nodeType) {
                case 3: // Text node
                    start = escapeTextForHtml(node.nodeValue);
                    break;
                case 8: // Comment
                    start = "<!--" + escapeTextForHtml(node.nodeValue) + "-->";
                    break;
                default:
                    start = "<" + nodeInfo + ">";
                    end = "</>";
                    break;
            }
            if (start) {
                infoParts.push(start);
            }
            for (var i = 0; i < childCount; ++i) {
                nodeToInfoString(children[i], infoParts);
            }
            if (end) {
                infoParts.push(end);
            }
            return infoParts;
        }

        // Creates a string representation of the specified element's contents that is similar to innerHTML but omits all
        // attributes and comments and includes child node counts. This is done instead of using innerHTML to work around
        // IE <= 8's policy of including element properties in attributes, which ruins things by changing an element's
        // innerHTML whenever the user changes an input within the element.
        function getElementChecksum(el) {
            var info = nodeToInfoString(el).join("");
            return crc32(info).toString(16);
        }

        function serializePosition(node, offset, rootNode) {
            var pathParts = [], n = node;
            rootNode = rootNode || dom.getDocument(node).documentElement;
            while (n && n != rootNode) {
                pathParts.push(dom.getNodeIndex(n, true));
                n = n.parentNode;
            }
            return pathParts.join("/") + ":" + offset;
        }

        function deserializePosition(serialized, rootNode, doc) {
            if (!rootNode) {
                rootNode = (doc || document).documentElement;
            }
            var parts = serialized.split(":");
            var node = rootNode;
            var nodeIndices = parts[0] ? parts[0].split("/") : [], i = nodeIndices.length, nodeIndex;

            while (i--) {
                nodeIndex = parseInt(nodeIndices[i], 10);
                if (nodeIndex < node.childNodes.length) {
                    node = node.childNodes[nodeIndex];
                } else {
                    throw module.createError("deserializePosition() failed: node " + dom.inspectNode(node) +
                            " has no child with index " + nodeIndex + ", " + i);
                }
            }

            return new dom.DomPosition(node, parseInt(parts[1], 10));
        }

        function serializeRange(range, omitChecksum, rootNode) {
            rootNode = rootNode || api.DomRange.getRangeDocument(range).documentElement;
            if (!dom.isOrIsAncestorOf(rootNode, range.commonAncestorContainer)) {
                throw module.createError("serializeRange(): range " + range.inspect() +
                    " is not wholly contained within specified root node " + dom.inspectNode(rootNode));
            }
            var serialized = serializePosition(range.startContainer, range.startOffset, rootNode) + "," +
                serializePosition(range.endContainer, range.endOffset, rootNode);
            if (!omitChecksum) {
                serialized += "{" + getElementChecksum(rootNode) + "}";
            }
            return serialized;
        }

        var deserializeRegex = /^([^,]+),([^,\{]+)(\{([^}]+)\})?$/;

        function deserializeRange(serialized, rootNode, doc) {
            if (rootNode) {
                doc = doc || dom.getDocument(rootNode);
            } else {
                doc = doc || document;
                rootNode = doc.documentElement;
            }
            var result = deserializeRegex.exec(serialized);
            var checksum = result[4];
            if (checksum) {
                var rootNodeChecksum = getElementChecksum(rootNode);
                if (checksum !== rootNodeChecksum) {
                    throw module.createError("deserializeRange(): checksums of serialized range root node (" + checksum +
                        ") and target root node (" + rootNodeChecksum + ") do not match");
                }
            }
            var start = deserializePosition(result[1], rootNode, doc), end = deserializePosition(result[2], rootNode, doc);
            var range = api.createRange(doc);
            range.setStartAndEnd(start.node, start.offset, end.node, end.offset);
            return range;
        }

        function canDeserializeRange(serialized, rootNode, doc) {
            if (!rootNode) {
                rootNode = (doc || document).documentElement;
            }
            var result = deserializeRegex.exec(serialized);
            var checksum = result[3];
            return !checksum || checksum === getElementChecksum(rootNode);
        }

        function serializeSelection(selection, omitChecksum, rootNode) {
            selection = api.getSelection(selection);
            var ranges = selection.getAllRanges(), serializedRanges = [];
            for (var i = 0, len = ranges.length; i < len; ++i) {
                serializedRanges[i] = serializeRange(ranges[i], omitChecksum, rootNode);
            }
            return serializedRanges.join("|");
        }

        function deserializeSelection(serialized, rootNode, win) {
            if (rootNode) {
                win = win || dom.getWindow(rootNode);
            } else {
                win = win || window;
                rootNode = win.document.documentElement;
            }
            var serializedRanges = serialized.split("|");
            var sel = api.getSelection(win);
            var ranges = [];

            for (var i = 0, len = serializedRanges.length; i < len; ++i) {
                ranges[i] = deserializeRange(serializedRanges[i], rootNode, win.document);
            }
            sel.setRanges(ranges);

            return sel;
        }

        function canDeserializeSelection(serialized, rootNode, win) {
            var doc;
            if (rootNode) {
                doc = win ? win.document : dom.getDocument(rootNode);
            } else {
                win = win || window;
                rootNode = win.document.documentElement;
            }
            var serializedRanges = serialized.split("|");

            for (var i = 0, len = serializedRanges.length; i < len; ++i) {
                if (!canDeserializeRange(serializedRanges[i], rootNode, doc)) {
                    return false;
                }
            }

            return true;
        }

        var cookieName = "rangySerializedSelection";

        function getSerializedSelectionFromCookie(cookie) {
            var parts = cookie.split(/[;,]/);
            for (var i = 0, len = parts.length, nameVal, val; i < len; ++i) {
                nameVal = parts[i].split("=");
                if (nameVal[0].replace(/^\s+/, "") == cookieName) {
                    val = nameVal[1];
                    if (val) {
                        return decodeURIComponent(val.replace(/\s+$/, ""));
                    }
                }
            }
            return null;
        }

        function restoreSelectionFromCookie(win) {
            win = win || window;
            var serialized = getSerializedSelectionFromCookie(win.document.cookie);
            if (serialized) {
                deserializeSelection(serialized, win.doc);
            }
        }

        function saveSelectionCookie(win, props) {
            win = win || window;
            props = (typeof props == "object") ? props : {};
            var expires = props.expires ? ";expires=" + props.expires.toUTCString() : "";
            var path = props.path ? ";path=" + props.path : "";
            var domain = props.domain ? ";domain=" + props.domain : "";
            var secure = props.secure ? ";secure" : "";
            var serialized = serializeSelection(api.getSelection(win));
            win.document.cookie = encodeURIComponent(cookieName) + "=" + encodeURIComponent(serialized) + expires + path + domain + secure;
        }

        util.extend(api, {
            serializePosition: serializePosition,
            deserializePosition: deserializePosition,
            serializeRange: serializeRange,
            deserializeRange: deserializeRange,
            canDeserializeRange: canDeserializeRange,
            serializeSelection: serializeSelection,
            deserializeSelection: deserializeSelection,
            canDeserializeSelection: canDeserializeSelection,
            restoreSelectionFromCookie: restoreSelectionFromCookie,
            saveSelectionCookie: saveSelectionCookie,
            getElementChecksum: getElementChecksum,
            nodeToInfoString: nodeToInfoString
        });

        util.crc32 = crc32;
    });
    
    return rangy;
}, this);
/**
 * Text range module for Rangy.
 * Text-based manipulation and searching of ranges and selections.
 *
 * Features
 *
 * - Ability to move range boundaries by character or word offsets
 * - Customizable word tokenizer
 * - Ignores text nodes inside <script> or <style> elements or those hidden by CSS display and visibility properties
 * - Range findText method to search for text or regex within the page or within a range. Flags for whole words and case
 *   sensitivity
 * - Selection and range save/restore as text offsets within a node
 * - Methods to return visible text within a range or selection
 * - innerText method for elements
 *
 * References
 *
 * https://www.w3.org/Bugs/Public/show_bug.cgi?id=13145
 * http://aryeh.name/spec/innertext/innertext.html
 * http://dvcs.w3.org/hg/editing/raw-file/tip/editing.html
 *
 * Part of Rangy, a cross-browser JavaScript range and selection library
 * https://github.com/timdown/rangy
 *
 * Depends on Rangy core.
 *
 * Copyright 2024, Tim Down
 * Licensed under the MIT license.
 * Version: 1.3.2
 * Build date: 2 November 2024
 */

/**
 * Problem: handling of trailing spaces before line breaks is handled inconsistently between browsers.
 *
 * First, a <br>: this is relatively simple. For the following HTML:
 *
 * 1 <br>2
 *
 * - IE and WebKit render the space, include it in the selection (i.e. when the content is selected and pasted into a
 *   textarea, the space is present) and allow the caret to be placed after it.
 * - Firefox does not acknowledge the space in the selection but it is possible to place the caret after it.
 * - Opera does not render the space but has two separate caret positions on either side of the space (left and right
 *   arrow keys show this) and includes the space in the selection.
 *
 * The other case is the line break or breaks implied by block elements. For the following HTML:
 *
 * <p>1 </p><p>2<p>
 *
 * - WebKit does not acknowledge the space in any way
 * - Firefox, IE and Opera as per <br>
 *
 * One more case is trailing spaces before line breaks in elements with white-space: pre-line. For the following HTML:
 *
 * <p style="white-space: pre-line">1
 * 2</p>
 *
 * - Firefox and WebKit include the space in caret positions
 * - IE does not support pre-line up to and including version 9
 * - Opera ignores the space
 * - Trailing space only renders if there is a non-collapsed character in the line
 *
 * Problem is whether Rangy should ever acknowledge the space and if so, when. Another problem is whether this can be
 * feature-tested
 */
(function(factory, root) {
    if (typeof define == "function" && define.amd) {
        // AMD. Register as an anonymous module with a dependency on Rangy.
        define(["./rangy-core"], factory);
    } else if (typeof module != "undefined" && typeof exports == "object") {
        // Node/CommonJS style
        module.exports = factory( require("rangy") );
    } else {
        // No AMD or CommonJS support so we use the rangy property of root (probably the global variable)
        factory(root.rangy);
    }
})(function(rangy) {
    rangy.createModule("TextRange", ["WrappedSelection"], function(api, module) {
        var UNDEF = "undefined";
        var CHARACTER = "character", WORD = "word";
        var dom = api.dom, util = api.util;
        var extend = util.extend;
        var createOptions = util.createOptions;
        var getBody = dom.getBody;


        var spacesRegex = /^[ \t\f\r\n]+$/;
        var spacesMinusLineBreaksRegex = /^[ \t\f\r]+$/;
        var allWhiteSpaceRegex = /^[\t-\r \u0085\u00A0\u1680\u180E\u2000-\u200B\u2028\u2029\u202F\u205F\u3000]+$/;
        var nonLineBreakWhiteSpaceRegex = /^[\t \u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000]+$/;
        var lineBreakRegex = /^[\n-\r\u0085\u2028\u2029]$/;

        var defaultLanguage = "en";

        var isDirectionBackward = api.Selection.isDirectionBackward;

        // Properties representing whether trailing spaces inside blocks are completely collapsed (as they are in WebKit,
        // but not other browsers). Also test whether trailing spaces before <br> elements are collapsed.
        var trailingSpaceInBlockCollapses = false;
        var trailingSpaceBeforeBrCollapses = false;
        var trailingSpaceBeforeBlockCollapses = false;
        var trailingSpaceBeforeLineBreakInPreLineCollapses = true;

        (function() {
            var el = dom.createTestElement(document, "<p>1 </p><p></p>", true);
            var p = el.firstChild;
            var sel = api.getSelection();
            sel.collapse(p.lastChild, 2);
            sel.setStart(p.firstChild, 0);
            trailingSpaceInBlockCollapses = ("" + sel).length == 1;

            el.innerHTML = "1 <br />";
            sel.collapse(el, 2);
            sel.setStart(el.firstChild, 0);
            trailingSpaceBeforeBrCollapses = ("" + sel).length == 1;

            el.innerHTML = "1 <p>1</p>";
            sel.collapse(el, 2);
            sel.setStart(el.firstChild, 0);
            trailingSpaceBeforeBlockCollapses = ("" + sel).length == 1;

            dom.removeNode(el);
            sel.removeAllRanges();
        })();

        /*----------------------------------------------------------------------------------------------------------------*/

        // This function must create word and non-word tokens for the whole of the text supplied to it
        function defaultTokenizer(chars, wordOptions) {
            var word = chars.join(""), result, tokenRanges = [];

            function createTokenRange(start, end, isWord) {
                tokenRanges.push( { start: start, end: end, isWord: isWord } );
            }

            // Match words and mark characters
            var lastWordEnd = 0, wordStart, wordEnd;
            while ( (result = wordOptions.wordRegex.exec(word)) ) {
                wordStart = result.index;
                wordEnd = wordStart + result[0].length;

                // Create token for non-word characters preceding this word
                if (wordStart > lastWordEnd) {
                    createTokenRange(lastWordEnd, wordStart, false);
                }

                // Get trailing space characters for word
                if (wordOptions.includeTrailingSpace) {
                    while ( nonLineBreakWhiteSpaceRegex.test(chars[wordEnd]) ) {
                        ++wordEnd;
                    }
                }
                createTokenRange(wordStart, wordEnd, true);
                lastWordEnd = wordEnd;
            }

            // Create token for trailing non-word characters, if any exist
            if (lastWordEnd < chars.length) {
                createTokenRange(lastWordEnd, chars.length, false);
            }

            return tokenRanges;
        }

        function convertCharRangeToToken(chars, tokenRange) {
            var tokenChars = chars.slice(tokenRange.start, tokenRange.end);
            var token = {
                isWord: tokenRange.isWord,
                chars: tokenChars,
                toString: function() {
                    return tokenChars.join("");
                }
            };
            for (var i = 0, len = tokenChars.length; i < len; ++i) {
                tokenChars[i].token = token;
            }
            return token;
        }

        function tokenize(chars, wordOptions, tokenizer) {
            var tokenRanges = tokenizer(chars, wordOptions);
            var tokens = [];
            for (var i = 0, tokenRange; tokenRange = tokenRanges[i++]; ) {
                tokens.push( convertCharRangeToToken(chars, tokenRange) );
            }
            return tokens;
        }

        var defaultCharacterOptions = {
            includeBlockContentTrailingSpace: true,
            includeSpaceBeforeBr: true,
            includeSpaceBeforeBlock: true,
            includePreLineTrailingSpace: true,
            ignoreCharacters: ""
        };

        function normalizeIgnoredCharacters(ignoredCharacters) {
            // Check if character is ignored
            var ignoredChars = ignoredCharacters || "";

            // Normalize ignored characters into a string consisting of characters in ascending order of character code
            var ignoredCharsArray = (typeof ignoredChars == "string") ? ignoredChars.split("") : ignoredChars;
            ignoredCharsArray.sort(function(char1, char2) {
                return char1.charCodeAt(0) - char2.charCodeAt(0);
            });

            /// Convert back to a string and remove duplicates
            return ignoredCharsArray.join("").replace(/(.)\1+/g, "$1");
        }

        var defaultCaretCharacterOptions = {
            includeBlockContentTrailingSpace: !trailingSpaceBeforeLineBreakInPreLineCollapses,
            includeSpaceBeforeBr: !trailingSpaceBeforeBrCollapses,
            includeSpaceBeforeBlock: !trailingSpaceBeforeBlockCollapses,
            includePreLineTrailingSpace: true
        };

        var defaultWordOptions = {
            "en": {
                wordRegex: /[a-z0-9]+('[a-z0-9]+)*/gi,
                includeTrailingSpace: false,
                tokenizer: defaultTokenizer
            }
        };

        var defaultFindOptions = {
            caseSensitive: false,
            withinRange: null,
            wholeWordsOnly: false,
            wrap: false,
            direction: "forward",
            wordOptions: null,
            characterOptions: null
        };

        var defaultMoveOptions = {
            wordOptions: null,
            characterOptions: null
        };

        var defaultExpandOptions = {
            wordOptions: null,
            characterOptions: null,
            trim: false,
            trimStart: true,
            trimEnd: true
        };

        var defaultWordIteratorOptions = {
            wordOptions: null,
            characterOptions: null,
            direction: "forward"
        };

        function createWordOptions(options) {
            var lang, defaults;
            if (!options) {
                return defaultWordOptions[defaultLanguage];
            } else {
                lang = options.language || defaultLanguage;
                defaults = {};
                extend(defaults, defaultWordOptions[lang] || defaultWordOptions[defaultLanguage]);
                extend(defaults, options);
                return defaults;
            }
        }

        function createNestedOptions(optionsParam, defaults) {
            var options = createOptions(optionsParam, defaults);
            if (defaults.hasOwnProperty("wordOptions")) {
                options.wordOptions = createWordOptions(options.wordOptions);
            }
            if (defaults.hasOwnProperty("characterOptions")) {
                options.characterOptions = createOptions(options.characterOptions, defaultCharacterOptions);
            }
            return options;
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        /* DOM utility functions */
        var getComputedStyleProperty = dom.getComputedStyleProperty;

        // Create cachable versions of DOM functions

        // Test for old IE's incorrect display properties
        var tableCssDisplayBlock;
        (function() {
            var table = document.createElement("table");
            var body = getBody(document);
            body.appendChild(table);
            tableCssDisplayBlock = (getComputedStyleProperty(table, "display") == "block");
            body.removeChild(table);
        })();

        var defaultDisplayValueForTag = {
            table: "table",
            caption: "table-caption",
            colgroup: "table-column-group",
            col: "table-column",
            thead: "table-header-group",
            tbody: "table-row-group",
            tfoot: "table-footer-group",
            tr: "table-row",
            td: "table-cell",
            th: "table-cell"
        };

        // Corrects IE's "block" value for table-related elements
        function getComputedDisplay(el, win) {
            var display = getComputedStyleProperty(el, "display", win);
            var tagName = el.tagName.toLowerCase();
            return (display == "block" &&
                    tableCssDisplayBlock &&
                    defaultDisplayValueForTag.hasOwnProperty(tagName)) ?
                defaultDisplayValueForTag[tagName] : display;
        }

        function isHidden(node) {
            var ancestors = getAncestorsAndSelf(node);
            for (var i = 0, len = ancestors.length; i < len; ++i) {
                if (ancestors[i].nodeType == 1 && getComputedDisplay(ancestors[i]) == "none") {
                    return true;
                }
            }

            return false;
        }

        function isVisibilityHiddenTextNode(textNode) {
            var el;
            return textNode.nodeType == 3 &&
                (el = textNode.parentNode) &&
                getComputedStyleProperty(el, "visibility") == "hidden";
        }

        /*----------------------------------------------------------------------------------------------------------------*/

    
        // "A block node is either an Element whose "display" property does not have
        // resolved value "inline" or "inline-block" or "inline-table" or "none", or a
        // Document, or a DocumentFragment."
        function isBlockNode(node) {
            return node &&
                ((node.nodeType == 1 && !/^(inline(-block|-table)?|none)$/.test(getComputedDisplay(node))) ||
                node.nodeType == 9 || node.nodeType == 11);
        }

        function getLastDescendantOrSelf(node) {
            var lastChild = node.lastChild;
            return lastChild ? getLastDescendantOrSelf(lastChild) : node;
        }

        function containsPositions(node) {
            return dom.isCharacterDataNode(node) ||
                !/^(area|base|basefont|br|col|frame|hr|img|input|isindex|link|meta|param)$/i.test(node.nodeName);
        }

        function getAncestors(node) {
            var ancestors = [];
            while (node.parentNode) {
                ancestors.unshift(node.parentNode);
                node = node.parentNode;
            }
            return ancestors;
        }

        function getAncestorsAndSelf(node) {
            return getAncestors(node).concat([node]);
        }

        function nextNodeDescendants(node) {
            while (node && !node.nextSibling) {
                node = node.parentNode;
            }
            if (!node) {
                return null;
            }
            return node.nextSibling;
        }

        function nextNode(node, excludeChildren) {
            if (!excludeChildren && node.hasChildNodes()) {
                return node.firstChild;
            }
            return nextNodeDescendants(node);
        }

        function previousNode(node) {
            var previous = node.previousSibling;
            if (previous) {
                node = previous;
                while (node.hasChildNodes()) {
                    node = node.lastChild;
                }
                return node;
            }
            var parent = node.parentNode;
            if (parent && parent.nodeType == 1) {
                return parent;
            }
            return null;
        }

        // Adpated from Aryeh's code.
        // "A whitespace node is either a Text node whose data is the empty string; or
        // a Text node whose data consists only of one or more tabs (0x0009), line
        // feeds (0x000A), carriage returns (0x000D), and/or spaces (0x0020), and whose
        // parent is an Element whose resolved value for "white-space" is "normal" or
        // "nowrap"; or a Text node whose data consists only of one or more tabs
        // (0x0009), carriage returns (0x000D), and/or spaces (0x0020), and whose
        // parent is an Element whose resolved value for "white-space" is "pre-line"."
        function isWhitespaceNode(node) {
            if (!node || node.nodeType != 3) {
                return false;
            }
            var text = node.data;
            if (text === "") {
                return true;
            }
            var parent = node.parentNode;
            if (!parent || parent.nodeType != 1) {
                return false;
            }
            var computedWhiteSpace = getComputedStyleProperty(node.parentNode, "whiteSpace");

            return (/^[\t\n\r ]+$/.test(text) && /^(normal|nowrap)$/.test(computedWhiteSpace)) ||
                (/^[\t\r ]+$/.test(text) && computedWhiteSpace == "pre-line");
        }

        // Adpated from Aryeh's code.
        // "node is a collapsed whitespace node if the following algorithm returns
        // true:"
        function isCollapsedWhitespaceNode(node) {
            // "If node's data is the empty string, return true."
            if (node.data === "") {
                return true;
            }

            // "If node is not a whitespace node, return false."
            if (!isWhitespaceNode(node)) {
                return false;
            }

            // "Let ancestor be node's parent."
            var ancestor = node.parentNode;

            // "If ancestor is null, return true."
            if (!ancestor) {
                return true;
            }

            // "If the "display" property of some ancestor of node has resolved value "none", return true."
            if (isHidden(node)) {
                return true;
            }

            return false;
        }

        function isCollapsedNode(node) {
            var type = node.nodeType;
            return type == 7 /* PROCESSING_INSTRUCTION */ ||
                type == 8 /* COMMENT */ ||
                isHidden(node) ||
                /^(script|style)$/i.test(node.nodeName) ||
                isVisibilityHiddenTextNode(node) ||
                isCollapsedWhitespaceNode(node);
        }

        function isIgnoredNode(node, win) {
            var type = node.nodeType;
            return type == 7 /* PROCESSING_INSTRUCTION */ ||
                type == 8 /* COMMENT */ ||
                (type == 1 && getComputedDisplay(node, win) == "none");
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        // Possibly overengineered caching system to prevent repeated DOM calls slowing everything down

        function Cache() {
            this.store = {};
        }

        Cache.prototype = {
            get: function(key) {
                return this.store.hasOwnProperty(key) ? this.store[key] : null;
            },

            set: function(key, value) {
                return this.store[key] = value;
            }
        };

        var cachedCount = 0, uncachedCount = 0;

        function createCachingGetter(methodName, func, objProperty) {
            return function(args) {
                var cache = this.cache;
                if (cache.hasOwnProperty(methodName)) {
                    cachedCount++;
                    return cache[methodName];
                } else {
                    uncachedCount++;
                    var value = func.call(this, objProperty ? this[objProperty] : this, args);
                    cache[methodName] = value;
                    return value;
                }
            };
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        function NodeWrapper(node, session) {
            this.node = node;
            this.session = session;
            this.cache = new Cache();
            this.positions = new Cache();
        }

        var nodeProto = {
            getPosition: function(offset) {
                var positions = this.positions;
                return positions.get(offset) || positions.set(offset, new Position(this, offset));
            },

            toString: function() {
                return "[NodeWrapper(" + dom.inspectNode(this.node) + ")]";
            }
        };

        NodeWrapper.prototype = nodeProto;

        var EMPTY = "EMPTY",
            NON_SPACE = "NON_SPACE",
            UNCOLLAPSIBLE_SPACE = "UNCOLLAPSIBLE_SPACE",
            COLLAPSIBLE_SPACE = "COLLAPSIBLE_SPACE",
            TRAILING_SPACE_BEFORE_BLOCK = "TRAILING_SPACE_BEFORE_BLOCK",
            TRAILING_SPACE_IN_BLOCK = "TRAILING_SPACE_IN_BLOCK",
            TRAILING_SPACE_BEFORE_BR = "TRAILING_SPACE_BEFORE_BR",
            PRE_LINE_TRAILING_SPACE_BEFORE_LINE_BREAK = "PRE_LINE_TRAILING_SPACE_BEFORE_LINE_BREAK",
            TRAILING_LINE_BREAK_AFTER_BR = "TRAILING_LINE_BREAK_AFTER_BR",
            INCLUDED_TRAILING_LINE_BREAK_AFTER_BR = "INCLUDED_TRAILING_LINE_BREAK_AFTER_BR";

        extend(nodeProto, {
            isCharacterDataNode: createCachingGetter("isCharacterDataNode", dom.isCharacterDataNode, "node"),
            getNodeIndex: createCachingGetter("nodeIndex", dom.getNodeIndex, "node"),
            getLength: createCachingGetter("nodeLength", dom.getNodeLength, "node"),
            containsPositions: createCachingGetter("containsPositions", containsPositions, "node"),
            isWhitespace: createCachingGetter("isWhitespace", isWhitespaceNode, "node"),
            isCollapsedWhitespace: createCachingGetter("isCollapsedWhitespace", isCollapsedWhitespaceNode, "node"),
            getComputedDisplay: createCachingGetter("computedDisplay", getComputedDisplay, "node"),
            isCollapsed: createCachingGetter("collapsed", isCollapsedNode, "node"),
            isIgnored: createCachingGetter("ignored", isIgnoredNode, "node"),
            next: createCachingGetter("nextPos", nextNode, "node"),
            previous: createCachingGetter("previous", previousNode, "node"),

            getTextNodeInfo: createCachingGetter("textNodeInfo", function(textNode) {
                var spaceRegex = null, collapseSpaces = false;
                var cssWhitespace = getComputedStyleProperty(textNode.parentNode, "whiteSpace");
                var preLine = (cssWhitespace == "pre-line");
                if (preLine) {
                    spaceRegex = spacesMinusLineBreaksRegex;
                    collapseSpaces = true;
                } else if (cssWhitespace == "normal" || cssWhitespace == "nowrap") {
                    spaceRegex = spacesRegex;
                    collapseSpaces = true;
                }

                return {
                    node: textNode,
                    text: textNode.data,
                    spaceRegex: spaceRegex,
                    collapseSpaces: collapseSpaces,
                    preLine: preLine
                };
            }, "node"),

            hasInnerText: createCachingGetter("hasInnerText", function(el, backward) {
                var session = this.session;
                var posAfterEl = session.getPosition(el.parentNode, this.getNodeIndex() + 1);
                var firstPosInEl = session.getPosition(el, 0);

                var pos = backward ? posAfterEl : firstPosInEl;
                var endPos = backward ? firstPosInEl : posAfterEl;

                /*
                 <body><p>X  </p><p>Y</p></body>

                 Positions:

                 body:0:""
                 p:0:""
                 text:0:""
                 text:1:"X"
                 text:2:TRAILING_SPACE_IN_BLOCK
                 text:3:COLLAPSED_SPACE
                 p:1:""
                 body:1:"\n"
                 p:0:""
                 text:0:""
                 text:1:"Y"

                 A character is a TRAILING_SPACE_IN_BLOCK iff:

                 - There is no uncollapsed character after it within the visible containing block element

                 A character is a TRAILING_SPACE_BEFORE_BR iff:

                 - There is no uncollapsed character after it preceding a <br> element

                 An element has inner text iff

                 - It is not hidden
                 - It contains an uncollapsed character

                 All trailing spaces (pre-line, before <br>, end of block) require definite non-empty characters to render.
                 */

                while (pos !== endPos) {
                    pos.prepopulateChar();
                    if (pos.isDefinitelyNonEmpty()) {
                        return true;
                    }
                    pos = backward ? pos.previousVisible() : pos.nextVisible();
                }

                return false;
            }, "node"),

            isRenderedBlock: createCachingGetter("isRenderedBlock", function(el) {
                // Ensure that a block element containing a <br> is considered to have inner text
                var brs = el.getElementsByTagName("br");
                for (var i = 0, len = brs.length; i < len; ++i) {
                    if (!isCollapsedNode(brs[i])) {
                        return true;
                    }
                }
                return this.hasInnerText();
            }, "node"),

            getTrailingSpace: createCachingGetter("trailingSpace", function(el) {
                if (el.tagName.toLowerCase() == "br") {
                    return "";
                } else {
                    switch (this.getComputedDisplay()) {
                        case "inline":
                            var child = el.lastChild;
                            while (child) {
                                if (!isIgnoredNode(child)) {
                                    return (child.nodeType == 1) ? this.session.getNodeWrapper(child).getTrailingSpace() : "";
                                }
                                child = child.previousSibling;
                            }
                            break;
                        case "inline-block":
                        case "inline-table":
                        case "none":
                        case "table-column":
                        case "table-column-group":
                            break;
                        case "table-cell":
                            return "\t";
                        default:
                            return this.isRenderedBlock(true) ? "\n" : "";
                    }
                }
                return "";
            }, "node"),

            getLeadingSpace: createCachingGetter("leadingSpace", function(el) {
                switch (this.getComputedDisplay()) {
                    case "inline":
                    case "inline-block":
                    case "inline-table":
                    case "none":
                    case "table-column":
                    case "table-column-group":
                    case "table-cell":
                        break;
                    default:
                        return this.isRenderedBlock(false) ? "\n" : "";
                }
                return "";
            }, "node")
        });

        /*----------------------------------------------------------------------------------------------------------------*/

        function Position(nodeWrapper, offset) {
            this.offset = offset;
            this.nodeWrapper = nodeWrapper;
            this.node = nodeWrapper.node;
            this.session = nodeWrapper.session;
            this.cache = new Cache();
        }

        function inspectPosition() {
            return "[Position(" + dom.inspectNode(this.node) + ":" + this.offset + ")]";
        }

        var positionProto = {
            character: "",
            characterType: EMPTY,
            isBr: false,

            /*
            This method:
            - Fully populates positions that have characters that can be determined independently of any other characters.
            - Populates most types of space positions with a provisional character. The character is finalized later.
             */
            prepopulateChar: function() {
                var pos = this;
                if (!pos.prepopulatedChar) {
                    var node = pos.node, offset = pos.offset;
                    var visibleChar = "", charType = EMPTY;
                    var finalizedChar = false;
                    if (offset > 0) {
                        if (node.nodeType == 3) {
                            var text = node.data;
                            var textChar = text.charAt(offset - 1);

                            var nodeInfo = pos.nodeWrapper.getTextNodeInfo();
                            var spaceRegex = nodeInfo.spaceRegex;
                            if (nodeInfo.collapseSpaces) {
                                if (spaceRegex.test(textChar)) {
                                    // "If the character at position is from set, append a single space (U+0020) to newdata and advance
                                    // position until the character at position is not from set."

                                    // We also need to check for the case where we're in a pre-line and we have a space preceding a
                                    // line break, because such spaces are collapsed in some browsers
                                    if (offset > 1 && spaceRegex.test(text.charAt(offset - 2))) {
                                    } else if (nodeInfo.preLine && text.charAt(offset) === "\n") {
                                        visibleChar = " ";
                                        charType = PRE_LINE_TRAILING_SPACE_BEFORE_LINE_BREAK;
                                    } else {
                                        visibleChar = " ";
                                        //pos.checkForFollowingLineBreak = true;
                                        charType = COLLAPSIBLE_SPACE;
                                    }
                                } else {
                                    visibleChar = textChar;
                                    charType = NON_SPACE;
                                    finalizedChar = true;
                                }
                            } else {
                                visibleChar = textChar;
                                charType = UNCOLLAPSIBLE_SPACE;
                                finalizedChar = true;
                            }
                        } else {
                            var nodePassed = node.childNodes[offset - 1];
                            if (nodePassed && nodePassed.nodeType == 1 && !isCollapsedNode(nodePassed)) {
                                if (nodePassed.tagName.toLowerCase() == "br") {
                                    visibleChar = "\n";
                                    pos.isBr = true;
                                    charType = COLLAPSIBLE_SPACE;
                                    finalizedChar = false;
                                } else {
                                    pos.checkForTrailingSpace = true;
                                }
                            }

                            // Check the leading space of the next node for the case when a block element follows an inline
                            // element or text node. In that case, there is an implied line break between the two nodes.
                            if (!visibleChar) {
                                var nextNode = node.childNodes[offset];
                                if (nextNode && nextNode.nodeType == 1 && !isCollapsedNode(nextNode)) {
                                    pos.checkForLeadingSpace = true;
                                }
                            }
                        }
                    }

                    pos.prepopulatedChar = true;
                    pos.character = visibleChar;
                    pos.characterType = charType;
                    pos.isCharInvariant = finalizedChar;
                }
            },

            isDefinitelyNonEmpty: function() {
                var charType = this.characterType;
                return charType == NON_SPACE || charType == UNCOLLAPSIBLE_SPACE;
            },

            // Resolve leading and trailing spaces, which may involve prepopulating other positions
            resolveLeadingAndTrailingSpaces: function() {
                if (!this.prepopulatedChar) {
                    this.prepopulateChar();
                }
                if (this.checkForTrailingSpace) {
                    var trailingSpace = this.session.getNodeWrapper(this.node.childNodes[this.offset - 1]).getTrailingSpace();
                    if (trailingSpace) {
                        this.isTrailingSpace = true;
                        this.character = trailingSpace;
                        this.characterType = COLLAPSIBLE_SPACE;
                    }
                    this.checkForTrailingSpace = false;
                }
                if (this.checkForLeadingSpace) {
                    var leadingSpace = this.session.getNodeWrapper(this.node.childNodes[this.offset]).getLeadingSpace();
                    if (leadingSpace) {
                        this.isLeadingSpace = true;
                        this.character = leadingSpace;
                        this.characterType = COLLAPSIBLE_SPACE;
                    }
                    this.checkForLeadingSpace = false;
                }
            },

            getPrecedingUncollapsedPosition: function(characterOptions) {
                var pos = this, character;
                while ( (pos = pos.previousVisible()) ) {
                    character = pos.getCharacter(characterOptions);
                    if (character !== "") {
                        return pos;
                    }
                }

                return null;
            },

            getCharacter: function(characterOptions) {
                this.resolveLeadingAndTrailingSpaces();

                var thisChar = this.character, returnChar;

                // Check if character is ignored
                var ignoredChars = normalizeIgnoredCharacters(characterOptions.ignoreCharacters);
                var isIgnoredCharacter = (thisChar !== "" && ignoredChars.indexOf(thisChar) > -1);

                // Check if this position's  character is invariant (i.e. not dependent on character options) and return it
                // if so
                if (this.isCharInvariant) {
                    returnChar = isIgnoredCharacter ? "" : thisChar;
                    return returnChar;
                }

                var cacheKey = ["character", characterOptions.includeSpaceBeforeBr, characterOptions.includeBlockContentTrailingSpace, characterOptions.includePreLineTrailingSpace, ignoredChars].join("_");
                var cachedChar = this.cache.get(cacheKey);
                if (cachedChar !== null) {
                    return cachedChar;
                }

                // We need to actually get the character now
                var character = "";
                var collapsible = (this.characterType == COLLAPSIBLE_SPACE);

                var nextPos, previousPos;
                var gotPreviousPos = false;
                var pos = this;

                function getPreviousPos() {
                    if (!gotPreviousPos) {
                        previousPos = pos.getPrecedingUncollapsedPosition(characterOptions);
                        gotPreviousPos = true;
                    }
                    return previousPos;
                }

                // Disallow a collapsible space that is followed by a line break or is the last character
                if (collapsible) {
                    // Allow a trailing space that we've previously determined should be included
                    if (this.type == INCLUDED_TRAILING_LINE_BREAK_AFTER_BR) {
                        character = "\n";
                    }
                    // Disallow a collapsible space that follows a trailing space or line break, or is the first character,
                    // or follows a collapsible included space
                    else if (thisChar == " " &&
                            (!getPreviousPos() || previousPos.isTrailingSpace || previousPos.character == "\n" || (previousPos.character == " " && previousPos.characterType == COLLAPSIBLE_SPACE))) {
                    }
                    // Allow a leading line break unless it follows a line break
                    else if (thisChar == "\n" && this.isLeadingSpace) {
                        if (getPreviousPos() && previousPos.character != "\n") {
                            character = "\n";
                        } else {
                        }
                    } else {
                        nextPos = this.nextUncollapsed();
                        if (nextPos) {
                            if (nextPos.isBr) {
                                this.type = TRAILING_SPACE_BEFORE_BR;
                            } else if (nextPos.isTrailingSpace && nextPos.character == "\n") {
                                this.type = TRAILING_SPACE_IN_BLOCK;
                            } else if (nextPos.isLeadingSpace && nextPos.character == "\n") {
                                this.type = TRAILING_SPACE_BEFORE_BLOCK;
                            }

                            if (nextPos.character == "\n") {
                                if (this.type == TRAILING_SPACE_BEFORE_BR && !characterOptions.includeSpaceBeforeBr) {
                                } else if (this.type == TRAILING_SPACE_BEFORE_BLOCK && !characterOptions.includeSpaceBeforeBlock) {
                                } else if (this.type == TRAILING_SPACE_IN_BLOCK && nextPos.isTrailingSpace && !characterOptions.includeBlockContentTrailingSpace) {
                                } else if (this.type == PRE_LINE_TRAILING_SPACE_BEFORE_LINE_BREAK && nextPos.type == NON_SPACE && !characterOptions.includePreLineTrailingSpace) {
                                } else if (thisChar == "\n") {
                                    if (nextPos.isTrailingSpace) {
                                        if (this.isTrailingSpace) {
                                        } else if (this.isBr) {
                                            nextPos.type = TRAILING_LINE_BREAK_AFTER_BR;

                                            if (getPreviousPos() && previousPos.isLeadingSpace && !previousPos.isTrailingSpace && previousPos.character == "\n") {
                                                nextPos.character = "";
                                            } else {
                                                nextPos.type = INCLUDED_TRAILING_LINE_BREAK_AFTER_BR;
                                            }
                                        }
                                    } else {
                                        character = "\n";
                                    }
                                } else if (thisChar == " ") {
                                    character = " ";
                                } else {
                                }
                            } else {
                                character = thisChar;
                            }
                        } else {
                        }
                    }
                }

                if (ignoredChars.indexOf(character) > -1) {
                    character = "";
                }


                this.cache.set(cacheKey, character);

                return character;
            },

            equals: function(pos) {
                return !!pos && this.node === pos.node && this.offset === pos.offset;
            },

            inspect: inspectPosition,

            toString: function() {
                return this.character;
            }
        };

        Position.prototype = positionProto;

        extend(positionProto, {
            next: createCachingGetter("nextPos", function(pos) {
                var nodeWrapper = pos.nodeWrapper, node = pos.node, offset = pos.offset, session = nodeWrapper.session;
                if (!node) {
                    return null;
                }
                var nextNode, nextOffset, child;
                if (offset == nodeWrapper.getLength()) {
                    // Move onto the next node
                    nextNode = node.parentNode;
                    nextOffset = nextNode ? nodeWrapper.getNodeIndex() + 1 : 0;
                } else {
                    if (nodeWrapper.isCharacterDataNode()) {
                        nextNode = node;
                        nextOffset = offset + 1;
                    } else {
                        child = node.childNodes[offset];
                        // Go into the children next, if children there are
                        if (session.getNodeWrapper(child).containsPositions()) {
                            nextNode = child;
                            nextOffset = 0;
                        } else {
                            nextNode = node;
                            nextOffset = offset + 1;
                        }
                    }
                }

                return nextNode ? session.getPosition(nextNode, nextOffset) : null;
            }),

            previous: createCachingGetter("previous", function(pos) {
                var nodeWrapper = pos.nodeWrapper, node = pos.node, offset = pos.offset, session = nodeWrapper.session;
                var previousNode, previousOffset, child;
                if (offset == 0) {
                    previousNode = node.parentNode;
                    previousOffset = previousNode ? nodeWrapper.getNodeIndex() : 0;
                } else {
                    if (nodeWrapper.isCharacterDataNode()) {
                        previousNode = node;
                        previousOffset = offset - 1;
                    } else {
                        child = node.childNodes[offset - 1];
                        // Go into the children next, if children there are
                        if (session.getNodeWrapper(child).containsPositions()) {
                            previousNode = child;
                            previousOffset = dom.getNodeLength(child);
                        } else {
                            previousNode = node;
                            previousOffset = offset - 1;
                        }
                    }
                }
                return previousNode ? session.getPosition(previousNode, previousOffset) : null;
            }),

            /*
             Next and previous position moving functions that filter out

             - Hidden (CSS visibility/display) elements
             - Script and style elements
             */
            nextVisible: createCachingGetter("nextVisible", function(pos) {
                var next = pos.next();
                if (!next) {
                    return null;
                }
                var nodeWrapper = next.nodeWrapper, node = next.node;
                var newPos = next;
                if (nodeWrapper.isCollapsed()) {
                    // We're skipping this node and all its descendants
                    newPos = nodeWrapper.session.getPosition(node.parentNode, nodeWrapper.getNodeIndex() + 1);
                }
                return newPos;
            }),

            nextUncollapsed: createCachingGetter("nextUncollapsed", function(pos) {
                var nextPos = pos;
                while ( (nextPos = nextPos.nextVisible()) ) {
                    nextPos.resolveLeadingAndTrailingSpaces();
                    if (nextPos.character !== "") {
                        return nextPos;
                    }
                }
                return null;
            }),

            previousVisible: createCachingGetter("previousVisible", function(pos) {
                var previous = pos.previous();
                if (!previous) {
                    return null;
                }
                var nodeWrapper = previous.nodeWrapper, node = previous.node;
                var newPos = previous;
                if (nodeWrapper.isCollapsed()) {
                    // We're skipping this node and all its descendants
                    newPos = nodeWrapper.session.getPosition(node.parentNode, nodeWrapper.getNodeIndex());
                }
                return newPos;
            })
        });

        /*----------------------------------------------------------------------------------------------------------------*/

        var currentSession = null;

        var Session = (function() {
            function createWrapperCache(nodeProperty) {
                var cache = new Cache();

                return {
                    get: function(node) {
                        var wrappersByProperty = cache.get(node[nodeProperty]);
                        if (wrappersByProperty) {
                            for (var i = 0, wrapper; wrapper = wrappersByProperty[i++]; ) {
                                if (wrapper.node === node) {
                                    return wrapper;
                                }
                            }
                        }
                        return null;
                    },

                    set: function(nodeWrapper) {
                        var property = nodeWrapper.node[nodeProperty];
                        var wrappersByProperty = cache.get(property) || cache.set(property, []);
                        wrappersByProperty.push(nodeWrapper);
                    }
                };
            }

            var uniqueIDSupported = util.isHostProperty(document.documentElement, "uniqueID");

            function Session() {
                this.initCaches();
            }

            Session.prototype = {
                initCaches: function() {
                    this.elementCache = uniqueIDSupported ? (function() {
                        var elementsCache = new Cache();

                        return {
                            get: function(el) {
                                return elementsCache.get(el.uniqueID);
                            },

                            set: function(elWrapper) {
                                elementsCache.set(elWrapper.node.uniqueID, elWrapper);
                            }
                        };
                    })() : createWrapperCache("tagName");

                    // Store text nodes keyed by data, although we may need to truncate this
                    this.textNodeCache = createWrapperCache("data");
                    this.otherNodeCache = createWrapperCache("nodeName");
                },

                getNodeWrapper: function(node) {
                    var wrapperCache;
                    switch (node.nodeType) {
                        case 1:
                            wrapperCache = this.elementCache;
                            break;
                        case 3:
                            wrapperCache = this.textNodeCache;
                            break;
                        default:
                            wrapperCache = this.otherNodeCache;
                            break;
                    }

                    var wrapper = wrapperCache.get(node);
                    if (!wrapper) {
                        wrapper = new NodeWrapper(node, this);
                        wrapperCache.set(wrapper);
                    }
                    return wrapper;
                },

                getPosition: function(node, offset) {
                    return this.getNodeWrapper(node).getPosition(offset);
                },

                getRangeBoundaryPosition: function(range, isStart) {
                    var prefix = isStart ? "start" : "end";
                    return this.getPosition(range[prefix + "Container"], range[prefix + "Offset"]);
                },

                detach: function() {
                    this.elementCache = this.textNodeCache = this.otherNodeCache = null;
                }
            };

            return Session;
        })();

        /*----------------------------------------------------------------------------------------------------------------*/

        function startSession() {
            endSession();
            return (currentSession = new Session());
        }

        function getSession() {
            return currentSession || startSession();
        }

        function endSession() {
            if (currentSession) {
                currentSession.detach();
            }
            currentSession = null;
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        // Extensions to the rangy.dom utility object

        extend(dom, {
            nextNode: nextNode,
            previousNode: previousNode
        });

        /*----------------------------------------------------------------------------------------------------------------*/

        function createCharacterIterator(startPos, backward, endPos, characterOptions) {

            // Adjust the end position to ensure that it is actually reached
            if (endPos) {
                if (backward) {
                    if (isCollapsedNode(endPos.node)) {
                        endPos = startPos.previousVisible();
                    }
                } else {
                    if (isCollapsedNode(endPos.node)) {
                        endPos = endPos.nextVisible();
                    }
                }
            }

            var pos = startPos, finished = false;

            function next() {
                var charPos = null;
                if (backward) {
                    charPos = pos;
                    if (!finished) {
                        pos = pos.previousVisible();
                        finished = !pos || (endPos && pos.equals(endPos));
                    }
                } else {
                    if (!finished) {
                        charPos = pos = pos.nextVisible();
                        finished = !pos || (endPos && pos.equals(endPos));
                    }
                }
                if (finished) {
                    pos = null;
                }
                return charPos;
            }

            var previousTextPos, returnPreviousTextPos = false;

            return {
                next: function() {
                    if (returnPreviousTextPos) {
                        returnPreviousTextPos = false;
                        return previousTextPos;
                    } else {
                        var pos, character;
                        while ( (pos = next()) ) {
                            character = pos.getCharacter(characterOptions);
                            if (character) {
                                previousTextPos = pos;
                                return pos;
                            }
                        }
                        return null;
                    }
                },

                rewind: function() {
                    if (previousTextPos) {
                        returnPreviousTextPos = true;
                    } else {
                        throw module.createError("createCharacterIterator: cannot rewind. Only one position can be rewound.");
                    }
                },

                dispose: function() {
                    startPos = endPos = null;
                }
            };
        }

        var arrayIndexOf = Array.prototype.indexOf ?
            function(arr, val) {
                return arr.indexOf(val);
            } :
            function(arr, val) {
                for (var i = 0, len = arr.length; i < len; ++i) {
                    if (arr[i] === val) {
                        return i;
                    }
                }
                return -1;
            };

        // Provides a pair of iterators over text positions, tokenized. Transparently requests more text when next()
        // is called and there is no more tokenized text
        function createTokenizedTextProvider(pos, characterOptions, wordOptions) {
            var forwardIterator = createCharacterIterator(pos, false, null, characterOptions);
            var backwardIterator = createCharacterIterator(pos, true, null, characterOptions);
            var tokenizer = wordOptions.tokenizer;

            // Consumes a word and the whitespace beyond it
            function consumeWord(forward) {
                var pos, textChar;
                var newChars = [], it = forward ? forwardIterator : backwardIterator;

                var passedWordBoundary = false, insideWord = false;

                while ( (pos = it.next()) ) {
                    textChar = pos.character;


                    if (allWhiteSpaceRegex.test(textChar)) {
                        if (insideWord) {
                            insideWord = false;
                            passedWordBoundary = true;
                        }
                    } else {
                        if (passedWordBoundary) {
                            it.rewind();
                            break;
                        } else {
                            insideWord = true;
                        }
                    }
                    newChars.push(pos);
                }


                return newChars;
            }

            // Get initial word surrounding initial position and tokenize it
            var forwardChars = consumeWord(true);
            var backwardChars = consumeWord(false).reverse();
            var tokens = tokenize(backwardChars.concat(forwardChars), wordOptions, tokenizer);

            // Create initial token buffers
            var forwardTokensBuffer = forwardChars.length ?
                tokens.slice(arrayIndexOf(tokens, forwardChars[0].token)) : [];

            var backwardTokensBuffer = backwardChars.length ?
                tokens.slice(0, arrayIndexOf(tokens, backwardChars.pop().token) + 1) : [];

            function inspectBuffer(buffer) {
                var textPositions = ["[" + buffer.length + "]"];
                for (var i = 0; i < buffer.length; ++i) {
                    textPositions.push("(word: " + buffer[i] + ", is word: " + buffer[i].isWord + ")");
                }
                return textPositions;
            }


            return {
                nextEndToken: function() {
                    var lastToken, forwardChars;

                    // If we're down to the last token, consume character chunks until we have a word or run out of
                    // characters to consume
                    while ( forwardTokensBuffer.length == 1 &&
                        !(lastToken = forwardTokensBuffer[0]).isWord &&
                        (forwardChars = consumeWord(true)).length > 0) {

                        // Merge trailing non-word into next word and tokenize
                        forwardTokensBuffer = tokenize(lastToken.chars.concat(forwardChars), wordOptions, tokenizer);
                    }

                    return forwardTokensBuffer.shift();
                },

                previousStartToken: function() {
                    var lastToken, backwardChars;

                    // If we're down to the last token, consume character chunks until we have a word or run out of
                    // characters to consume
                    while ( backwardTokensBuffer.length == 1 &&
                        !(lastToken = backwardTokensBuffer[0]).isWord &&
                        (backwardChars = consumeWord(false)).length > 0) {

                        // Merge leading non-word into next word and tokenize
                        backwardTokensBuffer = tokenize(backwardChars.reverse().concat(lastToken.chars), wordOptions, tokenizer);
                    }

                    return backwardTokensBuffer.pop();
                },

                dispose: function() {
                    forwardIterator.dispose();
                    backwardIterator.dispose();
                    forwardTokensBuffer = backwardTokensBuffer = null;
                }
            };
        }

        function movePositionBy(pos, unit, count, characterOptions, wordOptions) {
            var unitsMoved = 0, currentPos, newPos = pos, charIterator, nextPos, absCount = Math.abs(count), token;
            if (count !== 0) {
                var backward = (count < 0);

                switch (unit) {
                    case CHARACTER:
                        charIterator = createCharacterIterator(pos, backward, null, characterOptions);
                        while ( (currentPos = charIterator.next()) && unitsMoved < absCount ) {
                            ++unitsMoved;
                            newPos = currentPos;
                        }
                        nextPos = currentPos;
                        charIterator.dispose();
                        break;
                    case WORD:
                        var tokenizedTextProvider = createTokenizedTextProvider(pos, characterOptions, wordOptions);
                        var next = backward ? tokenizedTextProvider.previousStartToken : tokenizedTextProvider.nextEndToken;

                        while ( (token = next()) && unitsMoved < absCount ) {
                            if (token.isWord) {
                                ++unitsMoved;
                                newPos = backward ? token.chars[0] : token.chars[token.chars.length - 1];
                            }
                        }
                        break;
                    default:
                        throw new Error("movePositionBy: unit '" + unit + "' not implemented");
                }

                // Perform any necessary position tweaks
                if (backward) {
                    newPos = newPos.previousVisible();
                    unitsMoved = -unitsMoved;
                } else if (newPos && newPos.isLeadingSpace && !newPos.isTrailingSpace) {
                    // Tweak the position for the case of a leading space. The problem is that an uncollapsed leading space
                    // before a block element (for example, the line break between "1" and "2" in the following HTML:
                    // "1<p>2</p>") is considered to be attached to the position immediately before the block element, which
                    // corresponds with a different selection position in most browsers from the one we want (i.e. at the
                    // start of the contents of the block element). We get round this by advancing the position returned to
                    // the last possible equivalent visible position.
                    if (unit == WORD) {
                        charIterator = createCharacterIterator(pos, false, null, characterOptions);
                        nextPos = charIterator.next();
                        charIterator.dispose();
                    }
                    if (nextPos) {
                        newPos = nextPos.previousVisible();
                    }
                }
            }


            return {
                position: newPos,
                unitsMoved: unitsMoved
            };
        }

        function createRangeCharacterIterator(session, range, characterOptions, backward) {
            var rangeStart = session.getRangeBoundaryPosition(range, true);
            var rangeEnd = session.getRangeBoundaryPosition(range, false);
            var itStart = backward ? rangeEnd : rangeStart;
            var itEnd = backward ? rangeStart : rangeEnd;

            return createCharacterIterator(itStart, !!backward, itEnd, characterOptions);
        }

        function getRangeCharacters(session, range, characterOptions) {

            var chars = [], it = createRangeCharacterIterator(session, range, characterOptions), pos;
            while ( (pos = it.next()) ) {
                chars.push(pos);
            }

            it.dispose();
            return chars;
        }

        function isWholeWord(startPos, endPos, wordOptions) {
            var range = api.createRange(startPos.node);
            range.setStartAndEnd(startPos.node, startPos.offset, endPos.node, endPos.offset);
            return !range.expand("word", { wordOptions: wordOptions });
        }

        function findTextFromPosition(initialPos, searchTerm, isRegex, searchScopeRange, findOptions) {
            var backward = isDirectionBackward(findOptions.direction);
            var it = createCharacterIterator(
                initialPos,
                backward,
                initialPos.session.getRangeBoundaryPosition(searchScopeRange, backward),
                findOptions.characterOptions
            );
            var text = "", chars = [], pos, currentChar, matchStartIndex, matchEndIndex;
            var result, insideRegexMatch;
            var returnValue = null;

            function handleMatch(startIndex, endIndex) {
                var startPos = chars[startIndex].previousVisible();
                var endPos = chars[endIndex - 1];
                var valid = (!findOptions.wholeWordsOnly || isWholeWord(startPos, endPos, findOptions.wordOptions));

                return {
                    startPos: startPos,
                    endPos: endPos,
                    valid: valid
                };
            }

            while ( (pos = it.next()) ) {
                currentChar = pos.character;
                if (!isRegex && !findOptions.caseSensitive) {
                    currentChar = currentChar.toLowerCase();
                }

                if (backward) {
                    chars.unshift(pos);
                    text = currentChar + text;
                } else {
                    chars.push(pos);
                    text += currentChar;
                }

                if (isRegex) {
                    result = searchTerm.exec(text);
                    if (result) {
                        matchStartIndex = result.index;
                        matchEndIndex = matchStartIndex + result[0].length;
                        if (insideRegexMatch) {
                            // Check whether the match is now over
                            if ((!backward && matchEndIndex < text.length) || (backward && matchStartIndex > 0)) {
                                returnValue = handleMatch(matchStartIndex, matchEndIndex);
                                break;
                            }
                        } else {
                            insideRegexMatch = true;
                        }
                    }
                } else if ( (matchStartIndex = text.indexOf(searchTerm)) != -1 ) {
                    returnValue = handleMatch(matchStartIndex, matchStartIndex + searchTerm.length);
                    break;
                }
            }

            // Check whether regex match extends to the end of the range
            if (insideRegexMatch) {
                returnValue = handleMatch(matchStartIndex, matchEndIndex);
            }
            it.dispose();

            return returnValue;
        }

        function createEntryPointFunction(func) {
            return function() {
                var sessionRunning = !!currentSession;
                var session = getSession();
                var args = [session].concat( util.toArray(arguments) );
                var returnValue = func.apply(this, args);
                if (!sessionRunning) {
                    endSession();
                }
                return returnValue;
            };
        }

        /*----------------------------------------------------------------------------------------------------------------*/

        // Extensions to the Rangy Range object

        function createRangeBoundaryMover(isStart, collapse) {
            /*
             Unit can be "character" or "word"
             Options:

             - includeTrailingSpace
             - wordRegex
             - tokenizer
             - collapseSpaceBeforeLineBreak
             */
            return createEntryPointFunction(
                function(session, unit, count, moveOptions) {
                    if (typeof count == UNDEF) {
                        count = unit;
                        unit = CHARACTER;
                    }
                    moveOptions = createNestedOptions(moveOptions, defaultMoveOptions);

                    var boundaryIsStart = isStart;
                    if (collapse) {
                        boundaryIsStart = (count >= 0);
                        this.collapse(!boundaryIsStart);
                    }
                    var moveResult = movePositionBy(session.getRangeBoundaryPosition(this, boundaryIsStart), unit, count, moveOptions.characterOptions, moveOptions.wordOptions);
                    var newPos = moveResult.position;
                    this[boundaryIsStart ? "setStart" : "setEnd"](newPos.node, newPos.offset);
                    return moveResult.unitsMoved;
                }
            );
        }

        function createRangeTrimmer(isStart) {
            return createEntryPointFunction(
                function(session, characterOptions) {
                    characterOptions = createOptions(characterOptions, defaultCharacterOptions);
                    var pos;
                    var it = createRangeCharacterIterator(session, this, characterOptions, !isStart);
                    var trimCharCount = 0;
                    while ( (pos = it.next()) && allWhiteSpaceRegex.test(pos.character) ) {
                        ++trimCharCount;
                    }
                    it.dispose();
                    var trimmed = (trimCharCount > 0);
                    if (trimmed) {
                        this[isStart ? "moveStart" : "moveEnd"](
                            "character",
                            isStart ? trimCharCount : -trimCharCount,
                            { characterOptions: characterOptions }
                        );
                    }
                    return trimmed;
                }
            );
        }

        extend(api.rangePrototype, {
            moveStart: createRangeBoundaryMover(true, false),

            moveEnd: createRangeBoundaryMover(false, false),

            move: createRangeBoundaryMover(true, true),

            trimStart: createRangeTrimmer(true),

            trimEnd: createRangeTrimmer(false),

            trim: createEntryPointFunction(
                function(session, characterOptions) {
                    var startTrimmed = this.trimStart(characterOptions), endTrimmed = this.trimEnd(characterOptions);
                    return startTrimmed || endTrimmed;
                }
            ),

            expand: createEntryPointFunction(
                function(session, unit, expandOptions) {
                    var moved = false;
                    expandOptions = createNestedOptions(expandOptions, defaultExpandOptions);
                    var characterOptions = expandOptions.characterOptions;
                    if (!unit) {
                        unit = CHARACTER;
                    }
                    if (unit == WORD) {
                        var wordOptions = expandOptions.wordOptions;
                        var startPos = session.getRangeBoundaryPosition(this, true);
                        var endPos = session.getRangeBoundaryPosition(this, false);

                        var startTokenizedTextProvider = createTokenizedTextProvider(startPos, characterOptions, wordOptions);
                        var startToken = startTokenizedTextProvider.nextEndToken();
                        var newStartPos = startToken.chars[0].previousVisible();
                        var endToken, newEndPos;

                        if (this.collapsed) {
                            endToken = startToken;
                        } else {
                            var endTokenizedTextProvider = createTokenizedTextProvider(endPos, characterOptions, wordOptions);
                            endToken = endTokenizedTextProvider.previousStartToken();
                        }
                        newEndPos = endToken.chars[endToken.chars.length - 1];

                        if (!newStartPos.equals(startPos)) {
                            this.setStart(newStartPos.node, newStartPos.offset);
                            moved = true;
                        }
                        if (newEndPos && !newEndPos.equals(endPos)) {
                            this.setEnd(newEndPos.node, newEndPos.offset);
                            moved = true;
                        }

                        if (expandOptions.trim) {
                            if (expandOptions.trimStart) {
                                moved = this.trimStart(characterOptions) || moved;
                            }
                            if (expandOptions.trimEnd) {
                                moved = this.trimEnd(characterOptions) || moved;
                            }
                        }

                        return moved;
                    } else {
                        return this.moveEnd(CHARACTER, 1, expandOptions);
                    }
                }
            ),

            text: createEntryPointFunction(
                function(session, characterOptions) {
                    return this.collapsed ?
                        "" : getRangeCharacters(session, this, createOptions(characterOptions, defaultCharacterOptions)).join("");
                }
            ),

            selectCharacters: createEntryPointFunction(
                function(session, containerNode, startIndex, endIndex, characterOptions) {
                    var moveOptions = { characterOptions: characterOptions };
                    if (!containerNode) {
                        containerNode = getBody( this.getDocument() );
                    }
                    this.selectNodeContents(containerNode);
                    this.collapse(true);
                    this.moveStart("character", startIndex, moveOptions);
                    this.collapse(true);
                    this.moveEnd("character", endIndex - startIndex, moveOptions);
                }
            ),

            // Character indexes are relative to the start of node
            toCharacterRange: createEntryPointFunction(
                function(session, containerNode, characterOptions) {
                    if (!containerNode) {
                        containerNode = getBody( this.getDocument() );
                    }
                    var parent = containerNode.parentNode, nodeIndex = dom.getNodeIndex(containerNode);
                    var rangeStartsBeforeNode = (dom.comparePoints(this.startContainer, this.endContainer, parent, nodeIndex) == -1);
                    var rangeBetween = this.cloneRange();
                    var startIndex, endIndex;
                    if (rangeStartsBeforeNode) {
                        rangeBetween.setStartAndEnd(this.startContainer, this.startOffset, parent, nodeIndex);
                        startIndex = -rangeBetween.text(characterOptions).length;
                    } else {
                        rangeBetween.setStartAndEnd(parent, nodeIndex, this.startContainer, this.startOffset);
                        startIndex = rangeBetween.text(characterOptions).length;
                    }
                    endIndex = startIndex + this.text(characterOptions).length;

                    return {
                        start: startIndex,
                        end: endIndex
                    };
                }
            ),

            findText: createEntryPointFunction(
                function(session, searchTermParam, findOptions) {
                    // Set up options
                    findOptions = createNestedOptions(findOptions, defaultFindOptions);

                    // Create word options if we're matching whole words only
                    if (findOptions.wholeWordsOnly) {
                        // We don't ever want trailing spaces for search results
                        findOptions.wordOptions.includeTrailingSpace = false;
                    }

                    var backward = isDirectionBackward(findOptions.direction);

                    // Create a range representing the search scope if none was provided
                    var searchScopeRange = findOptions.withinRange;
                    if (!searchScopeRange) {
                        searchScopeRange = api.createRange();
                        searchScopeRange.selectNodeContents(this.getDocument());
                    }

                    // Examine and prepare the search term
                    var searchTerm = searchTermParam, isRegex = false;
                    if (typeof searchTerm == "string") {
                        if (!findOptions.caseSensitive) {
                            searchTerm = searchTerm.toLowerCase();
                        }
                    } else {
                        isRegex = true;
                    }

                    var initialPos = session.getRangeBoundaryPosition(this, !backward);

                    // Adjust initial position if it lies outside the search scope
                    var comparison = searchScopeRange.comparePoint(initialPos.node, initialPos.offset);

                    if (comparison === -1) {
                        initialPos = session.getRangeBoundaryPosition(searchScopeRange, true);
                    } else if (comparison === 1) {
                        initialPos = session.getRangeBoundaryPosition(searchScopeRange, false);
                    }

                    var pos = initialPos;
                    var wrappedAround = false;

                    // Try to find a match and ignore invalid ones
                    var findResult;
                    while (true) {
                        findResult = findTextFromPosition(pos, searchTerm, isRegex, searchScopeRange, findOptions);

                        if (findResult) {
                            if (findResult.valid) {
                                this.setStartAndEnd(findResult.startPos.node, findResult.startPos.offset, findResult.endPos.node, findResult.endPos.offset);
                                return true;
                            } else {
                                // We've found a match that is not a whole word, so we carry on searching from the point immediately
                                // after the match
                                pos = backward ? findResult.startPos : findResult.endPos;
                            }
                        } else if (findOptions.wrap && !wrappedAround) {
                            // No result found but we're wrapping around and limiting the scope to the unsearched part of the range
                            searchScopeRange = searchScopeRange.cloneRange();
                            pos = session.getRangeBoundaryPosition(searchScopeRange, !backward);
                            searchScopeRange.setBoundary(initialPos.node, initialPos.offset, backward);
                            wrappedAround = true;
                        } else {
                            // Nothing found and we can't wrap around, so we're done
                            return false;
                        }
                    }
                }
            ),

            pasteHtml: function(html) {
                this.deleteContents();
                if (html) {
                    var frag = this.createContextualFragment(html);
                    var lastChild = frag.lastChild;
                    this.insertNode(frag);
                    this.collapseAfter(lastChild);
                }
            }
        });

        /*----------------------------------------------------------------------------------------------------------------*/

        // Extensions to the Rangy Selection object

        function createSelectionTrimmer(methodName) {
            return createEntryPointFunction(
                function(session, characterOptions) {
                    var trimmed = false;
                    this.changeEachRange(function(range) {
                        trimmed = range[methodName](characterOptions) || trimmed;
                    });
                    return trimmed;
                }
            );
        }

        extend(api.selectionPrototype, {
            expand: createEntryPointFunction(
                function(session, unit, expandOptions) {
                    this.changeEachRange(function(range) {
                        range.expand(unit, expandOptions);
                    });
                }
            ),

            move: createEntryPointFunction(
                function(session, unit, count, options) {
                    var unitsMoved = 0;
                    if (this.focusNode) {
                        this.collapse(this.focusNode, this.focusOffset);
                        var range = this.getRangeAt(0);
                        if (!options) {
                            options = {};
                        }
                        options.characterOptions = createOptions(options.characterOptions, defaultCaretCharacterOptions);
                        unitsMoved = range.move(unit, count, options);
                        this.setSingleRange(range);
                    }
                    return unitsMoved;
                }
            ),

            trimStart: createSelectionTrimmer("trimStart"),
            trimEnd: createSelectionTrimmer("trimEnd"),
            trim: createSelectionTrimmer("trim"),

            selectCharacters: createEntryPointFunction(
                function(session, containerNode, startIndex, endIndex, direction, characterOptions) {
                    var range = api.createRange(containerNode);
                    range.selectCharacters(containerNode, startIndex, endIndex, characterOptions);
                    this.setSingleRange(range, direction);
                }
            ),

            saveCharacterRanges: createEntryPointFunction(
                function(session, containerNode, characterOptions) {
                    var ranges = this.getAllRanges(), rangeCount = ranges.length;
                    var rangeInfos = [];

                    var backward = rangeCount == 1 && this.isBackward();

                    for (var i = 0, len = ranges.length; i < len; ++i) {
                        rangeInfos[i] = {
                            characterRange: ranges[i].toCharacterRange(containerNode, characterOptions),
                            backward: backward,
                            characterOptions: characterOptions
                        };
                    }

                    return rangeInfos;
                }
            ),

            restoreCharacterRanges: createEntryPointFunction(
                function(session, containerNode, saved) {
                    this.removeAllRanges();
                    for (var i = 0, len = saved.length, range, rangeInfo, characterRange; i < len; ++i) {
                        rangeInfo = saved[i];
                        characterRange = rangeInfo.characterRange;
                        range = api.createRange(containerNode);
                        range.selectCharacters(containerNode, characterRange.start, characterRange.end, rangeInfo.characterOptions);
                        this.addRange(range, rangeInfo.backward);
                    }
                }
            ),

            text: createEntryPointFunction(
                function(session, characterOptions) {
                    var rangeTexts = [];
                    for (var i = 0, len = this.rangeCount; i < len; ++i) {
                        rangeTexts[i] = this.getRangeAt(i).text(characterOptions);
                    }
                    return rangeTexts.join("");
                }
            )
        });

        /*----------------------------------------------------------------------------------------------------------------*/

        // Extensions to the core rangy object

        api.innerText = function(el, characterOptions) {
            var range = api.createRange(el);
            range.selectNodeContents(el);
            var text = range.text(characterOptions);
            return text;
        };

        api.createWordIterator = function(startNode, startOffset, iteratorOptions) {
            var session = getSession();
            iteratorOptions = createNestedOptions(iteratorOptions, defaultWordIteratorOptions);
            var startPos = session.getPosition(startNode, startOffset);
            var tokenizedTextProvider = createTokenizedTextProvider(startPos, iteratorOptions.characterOptions, iteratorOptions.wordOptions);
            var backward = isDirectionBackward(iteratorOptions.direction);

            return {
                next: function() {
                    return backward ? tokenizedTextProvider.previousStartToken() : tokenizedTextProvider.nextEndToken();
                },

                dispose: function() {
                    tokenizedTextProvider.dispose();
                    this.next = function() {};
                }
            };
        };

        /*----------------------------------------------------------------------------------------------------------------*/

        api.noMutation = function(func) {
            var session = getSession();
            func(session);
            endSession();
        };

        api.noMutation.createEntryPointFunction = createEntryPointFunction;

        api.textRange = {
            isBlockNode: isBlockNode,
            isCollapsedWhitespaceNode: isCollapsedWhitespaceNode,

            createPosition: createEntryPointFunction(
                function(session, node, offset) {
                    return session.getPosition(node, offset);
                }
            )
        };
    });
    
    return rangy;
}, this);
/**
 * vAccordion - AngularJS multi-level accordion component
 * @version v1.6.0
 * @link http://lukaszwatroba.github.io/v-accordion
 * @author Łukasz Wątroba <l@lukaszwatroba.com>
 * @license MIT License, http://www.opensource.org/licenses/MIT
 */

(function (angular) {
'use strict';

// Config
angular.module('vAccordion.config', [])
  .constant('accordionConfig', {
    states: {
      expanded: 'is-expanded'
    },
    expandAnimationDuration: 0.5
  })
  .animation('.is-expanded', [ '$animateCss', 'accordionConfig', function ($animateCss, accordionConfig) {
    return {
      addClass: function (element, className, done) {
        var paneContent = angular.element(element[0].querySelector('v-pane-content')),
            paneInner = angular.element(paneContent[0].querySelector('div'));

        var height = paneInner[0].offsetHeight;

        var expandAnimation = $animateCss(paneContent, {
          easing: 'ease',
          from: { maxHeight: '0px' },
          to: { maxHeight: height + 'px' },
          duration: accordionConfig.expandAnimationDuration
        });

        expandAnimation.start().done(function () {
          paneContent.css('max-height', 'none');
          done();
        });

        return function (isCancelled) {
          if (isCancelled) {
            paneContent.css('max-height', 'none');
          }
        };
      },
      removeClass: function (element, className, done) {
        var paneContent = angular.element(element[0].querySelector('v-pane-content')),
            paneInner = angular.element(paneContent[0].querySelector('div'));

        var height = paneInner[0].offsetHeight;

        var collapseAnimation = $animateCss(paneContent, {
          easing: 'ease',
          from: { maxHeight: height + 'px' },
          to: { maxHeight: '0px' },
          duration: accordionConfig.expandAnimationDuration
        });

        collapseAnimation.start().done(done);

        return function (isCancelled) {
          if (isCancelled) {
            paneContent.css('max-height', '0px');
          }
        };
      }
    };
  } ]);


// Modules
angular.module('vAccordion.directives', []);
angular.module('vAccordion',
  [
    'vAccordion.config',
    'vAccordion.directives'
  ]);



// vAccordion directive
angular.module('vAccordion.directives')
  .directive('vAccordion', vAccordionDirective);


function vAccordionDirective ($timeout) {
  return {
    restrict: 'E',
    transclude: true,
    controller: vAccordionController,
    scope: {
      control: '=?',
      expandCb: '&?onexpand',
      collapseCb: '&?oncollapse',
      id: '@?'
    },
    link: {
      pre: function (scope, iElement, iAttrs) {
        scope.allowMultiple = (angular.isDefined(iAttrs.multiple) && (iAttrs.multiple === '' || iAttrs.multiple === 'true'));
      },
      post: function (scope, iElement, iAttrs, ctrl, transclude) {
        transclude(scope.$parent.$new(), function (clone, transclusionScope) {
          transclusionScope.$accordion = scope.internalControl;
          if (scope.id) { transclusionScope.$accordion.id = scope.id; }
          iElement.append(clone);
        });

        iAttrs.$set('role', 'tablist');

        if (scope.allowMultiple) {
          iAttrs.$set('aria-multiselectable', 'true');
        }

        if (angular.isDefined(scope.control)) {
          checkCustomControlAPIMethods();

          var mergedControl = angular.extend({}, scope.internalControl, scope.control);
          scope.control = scope.internalControl = mergedControl;
        }
        else {
          scope.control = scope.internalControl;
        }

        function checkCustomControlAPIMethods () {
          var protectedApiMethods = ['toggle', 'expand', 'collapse', 'expandAll', 'collapseAll', 'hasExpandedPane'];

          angular.forEach(protectedApiMethods, function (iteratedMethodName) {
            if (scope.control[iteratedMethodName]) {
              throw new Error('The `' + iteratedMethodName + '` method can not be overwritten');
            }
          });
        }

        $timeout(function () {
          var eventName = (angular.isDefined(ctrl.getAccordionId())) ? ctrl.getAccordionId() + ':onReady' : 'vAccordion:onReady';
          scope.$emit(eventName);
        }, 0);
      }
    }
  };
}
vAccordionDirective.$inject = ['$timeout'];


// vAccordion directive controller
function vAccordionController ($scope) {
  var ctrl = this;
  var isDisabled = false;

  $scope.panes = [];

	$scope.expandCb = (angular.isFunction($scope.expandCb)) ? $scope.expandCb : angular.noop;
	$scope.collapseCb = (angular.isFunction($scope.collapseCb)) ? $scope.collapseCb : angular.noop;

  ctrl.hasExpandedPane = function hasExpandedPane () {
    var bool = false;

    for (var i = 0, length = $scope.panes.length; i < length; i++) {
      var iteratedPane = $scope.panes[i];

      if (iteratedPane.isExpanded) {
        bool = true;
        break;
      }
    }

    return bool;
  };

  ctrl.getPaneByIndex = function getPaneByIndex (index) {
    var thePane;

    angular.forEach($scope.panes, function (iteratedPane) {
      if (iteratedPane.$parent && angular.isDefined(iteratedPane.$parent.$index) && iteratedPane.$parent.$index === index) {
        thePane = iteratedPane;
      }
    });

    return (thePane) ? thePane : $scope.panes[index];
  };

  ctrl.getPaneIndex = function getPaneIndex (pane) {
    var theIndex;

    angular.forEach($scope.panes, function (iteratedPane) {
      if (iteratedPane.$parent && angular.isDefined(iteratedPane.$parent.$index) && iteratedPane === pane) {
        theIndex = iteratedPane.$parent.$index;
      }
    });

    return (angular.isDefined(theIndex)) ? theIndex : $scope.panes.indexOf(pane);
  };

  ctrl.getPaneById = function getPaneById (id) {
    var thePane;

    angular.forEach($scope.panes, function (iteratedPane) {
      if (iteratedPane.id && iteratedPane.id === id) {
        thePane = iteratedPane;
      }
    });

    return thePane;
  };

  ctrl.getPaneId = function getPaneId (pane) {
    return pane.id;
  };

  ctrl.getAccordionId = function getAccordionId () {
    return $scope.id;
  };


  ctrl.disable = function disable () {
    isDisabled = true;
  };

  ctrl.enable = function enable () {
    isDisabled = false;
  };

  ctrl.addPane = function addPane (paneToAdd) {
    if (!$scope.allowMultiple) {
      if (ctrl.hasExpandedPane() && paneToAdd.isExpanded) {
        throw new Error('The `multiple` attribute can\'t be found');
      }
    }

    $scope.panes.push(paneToAdd);

    if (paneToAdd.isExpanded) {
      $scope.expandCb({ index: ctrl.getPaneIndex(paneToAdd), id: paneToAdd.id, pane: paneToAdd });
    }
  };

  ctrl.focusNext = function focusNext () {
    var length = $scope.panes.length;

    for (var i = 0; i < length; i++) {
      var iteratedPane = $scope.panes[i];

      if (iteratedPane.isFocused) {
        var paneToFocusIndex = i + 1;

        if (paneToFocusIndex > $scope.panes.length - 1) {
          paneToFocusIndex = 0;
        }

        var paneToFocus = $scope.panes[paneToFocusIndex];
            paneToFocus.paneElement.find('v-pane-header')[0].focus();

        break;
      }
    }
  };

  ctrl.focusPrevious = function focusPrevious () {
    var length = $scope.panes.length;

    for (var i = 0; i < length; i++) {
      var iteratedPane = $scope.panes[i];

      if (iteratedPane.isFocused) {
        var paneToFocusIndex = i - 1;

        if (paneToFocusIndex < 0) {
          paneToFocusIndex = $scope.panes.length - 1;
        }

        var paneToFocus = $scope.panes[paneToFocusIndex];
            paneToFocus.paneElement.find('v-pane-header')[0].focus();

        break;
      }
    }
  };

  ctrl.toggle = function toggle (paneToToggle) {
    if (isDisabled || !paneToToggle) { return; }

    if (!$scope.allowMultiple) {
      ctrl.collapseAll(paneToToggle);
    }

    paneToToggle.isExpanded = !paneToToggle.isExpanded;

    if (paneToToggle.isExpanded) {
      $scope.expandCb({ index: ctrl.getPaneIndex(paneToToggle), id: paneToToggle.id, pane: paneToToggle });
    } else {
      $scope.collapseCb({ index: ctrl.getPaneIndex(paneToToggle), id: paneToToggle.id, pane: paneToToggle });
    }
  };

  ctrl.expand = function expand (paneToExpand) {
    if (isDisabled || !paneToExpand) { return; }

    if (!$scope.allowMultiple) {
      ctrl.collapseAll(paneToExpand);
    }

    if (!paneToExpand.isExpanded) {
      paneToExpand.isExpanded = true;
      $scope.expandCb({ index: ctrl.getPaneIndex(paneToExpand), id: paneToExpand.id, pane: paneToExpand });
    }
  };

  ctrl.collapse = function collapse (paneToCollapse) {
    if (isDisabled || !paneToCollapse) { return; }

    if (paneToCollapse.isExpanded) {
      paneToCollapse.isExpanded = false;
      $scope.collapseCb({ index: ctrl.getPaneIndex(paneToCollapse), id: paneToCollapse.id, pane: paneToCollapse });
    }
  };

  ctrl.expandAll = function expandAll () {
    if (isDisabled) { return; }

    if ($scope.allowMultiple) {
      angular.forEach($scope.panes, function (iteratedPane) {
        ctrl.expand(iteratedPane);
      });
    } else {
      throw new Error('The `multiple` attribute can\'t be found');
    }
  };

  ctrl.collapseAll = function collapseAll (exceptionalPane) {
    if (isDisabled) { return; }

    angular.forEach($scope.panes, function (iteratedPane) {
      if (iteratedPane !== exceptionalPane) {
        ctrl.collapse(iteratedPane);
      }
    });
  };

  // API
  $scope.internalControl = {
    toggle: function toggle (indexOrId) {
      if (angular.isString(indexOrId)) {
        ctrl.toggle( ctrl.getPaneById(indexOrId) );
      } else {
        ctrl.toggle( ctrl.getPaneByIndex(indexOrId) );
      }
    },
    expand: function expand (indexOrId) {
      if (angular.isString(indexOrId)) {
        ctrl.expand( ctrl.getPaneById(indexOrId) );
      } else {
        ctrl.expand( ctrl.getPaneByIndex(indexOrId) );
      }
    },
    collapse: function collapse (indexOrId) {
      if (angular.isString(indexOrId)) {
        ctrl.collapse( ctrl.getPaneById(indexOrId) );
      } else {
        ctrl.collapse( ctrl.getPaneByIndex(indexOrId) );
      }
    },
    expandAll: ctrl.expandAll,
    collapseAll: ctrl.collapseAll,
    hasExpandedPane: ctrl.hasExpandedPane
  };
}
vAccordionController.$inject = ['$scope'];



// vPane directive
angular.module('vAccordion.directives')
  .directive('vPane', vPaneDirective);


function vPaneDirective ($timeout, $animate, accordionConfig) {
  return {
    restrict: 'E',
    require: '^vAccordion',
    transclude: true,
    controller: vPaneController,
    scope: {
      isExpanded: '=?expanded',
      isDisabled: '=?ngDisabled',
      id: '@?'
    },
    link: function (scope, iElement, iAttrs, accordionCtrl, transclude) {
      transclude(scope.$parent.$new(), function (clone, transclusionScope) {
        transclusionScope.$pane = scope.internalControl;
        if (scope.id) { transclusionScope.$pane.id = scope.id; }
        iElement.append(clone);
      });

      if (!angular.isDefined(scope.isExpanded)) {
        scope.isExpanded = (angular.isDefined(iAttrs.expanded) && (iAttrs.expanded === ''));
      }

      if (angular.isDefined(iAttrs.disabled)) {
        scope.isDisabled = true;
      }

      var states = accordionConfig.states;

      var paneHeader = iElement.find('v-pane-header'),
          paneContent = iElement.find('v-pane-content'),
          paneInner = paneContent.find('div');

      var accordionId = accordionCtrl.getAccordionId();

      if (!paneHeader[0]) {
        throw new Error('The `v-pane-header` directive can\'t be found');
      }

      if (!paneContent[0]) {
        throw new Error('The `v-pane-content` directive can\'t be found');
      }

      scope.paneElement = iElement;
      scope.paneContentElement = paneContent;
      scope.paneInnerElement = paneInner;

      scope.accordionCtrl = accordionCtrl;

      accordionCtrl.addPane(scope);

      function emitEvent (eventName) {
        eventName = (angular.isDefined(accordionId)) ? accordionId + ':' + eventName : 'vAccordion:' + eventName;
        scope.$emit(eventName);
      }

      function expand () {
        accordionCtrl.disable();

        paneContent.attr('aria-hidden', 'false');

        paneHeader.attr({
          'aria-selected': 'true',
          'aria-expanded': 'true'
        });

        emitEvent('onExpand');

        $animate
          .addClass(iElement, states.expanded)
          .then(function () {
            accordionCtrl.enable();
            emitEvent('onExpandAnimationEnd');
          });
      }

      function collapse () {
        accordionCtrl.disable();

        paneContent.attr('aria-hidden', 'true');

        paneHeader.attr({
          'aria-selected': 'false',
          'aria-expanded': 'false'
        });

        emitEvent('onCollapse');

        $animate
          .removeClass(iElement, states.expanded)
          .then(function () {
            accordionCtrl.enable();
            emitEvent('onCollapseAnimationEnd');
          });
      }

      scope.$evalAsync(function () {
        if (scope.isExpanded) {
          iElement.addClass(states.expanded);
          paneContent
            .css('max-height', 'none')
            .attr('aria-hidden', 'false');

          paneHeader.attr({
            'aria-selected': 'true',
            'aria-expanded': 'true'
          });
        } else {
          paneContent
            .css('max-height', '0px')
            .attr('aria-hidden', 'true');

          paneHeader.attr({
            'aria-selected': 'false',
            'aria-expanded': 'false'
          });
        }
      });

      scope.$watch('isExpanded', function (newValue, oldValue) {
        if (newValue === oldValue) { return true; }
        if (newValue) { expand(); }
        else { collapse(); }
      });
    }
  };
}
vPaneDirective.$inject = ['$timeout', '$animate', 'accordionConfig'];


// vPane directive controller
function vPaneController ($scope) {
  var ctrl = this;

  ctrl.isExpanded = function isExpanded () {
    return $scope.isExpanded;
  };

  ctrl.toggle = function toggle () {
    if (!$scope.isAnimating && !$scope.isDisabled) {
      $scope.accordionCtrl.toggle($scope);
    }
  };

  ctrl.expand = function expand () {
    if (!$scope.isAnimating && !$scope.isDisabled) {
      $scope.accordionCtrl.expand($scope);
    }
  };

  ctrl.collapse = function collapse () {
    if (!$scope.isAnimating && !$scope.isDisabled) {
      $scope.accordionCtrl.collapse($scope);
    }
  };

  ctrl.focusPane = function focusPane () {
    $scope.isFocused = true;
  };

  ctrl.blurPane = function blurPane () {
    $scope.isFocused = false;
  };

  $scope.internalControl = {
    toggle: ctrl.toggle,
    expand: ctrl.expand,
    collapse: ctrl.collapse,
    isExpanded: ctrl.isExpanded
  };
}
vPaneController.$inject = ['$scope'];



// vPaneContent directive
angular.module('vAccordion.directives')
  .directive('vPaneContent', vPaneContentDirective);


function vPaneContentDirective () {
  return {
    restrict: 'E',
    require: '^vPane',
    transclude: true,
    template: '<div ng-transclude></div>',
    scope: {},
    link: function (scope, iElement, iAttrs) {
      iAttrs.$set('role', 'tabpanel');
      iAttrs.$set('aria-hidden', 'true');
    }
  };
}



// vPaneHeader directive
angular.module('vAccordion.directives')
  .directive('vPaneHeader', vPaneHeaderDirective);


function vPaneHeaderDirective () {
  return {
    restrict: 'E',
    require: ['^vPane', '^vAccordion'],
    transclude: true,
    template: '<div ng-transclude></div>',
    scope: {},
    link: function (scope, iElement, iAttrs, ctrls) {
      iAttrs.$set('role', 'tab');
      iAttrs.$set('tabindex', '0');

      var paneCtrl = ctrls[0],
          accordionCtrl = ctrls[1];

      var isInactive = angular.isDefined(iAttrs.inactive);

      function onClick () {
        if (isInactive) { return false; }
        scope.$apply(function () { paneCtrl.toggle(); });
      }

      function onKeydown (event) {
        if (event.keyCode === 32  || event.keyCode === 13) {
          scope.$apply(function () { paneCtrl.toggle(); });
          event.preventDefault();
        } else if (event.keyCode === 39 || event.keyCode === 40) {
          scope.$apply(function () { accordionCtrl.focusNext(); });
          event.preventDefault();
        } else if (event.keyCode === 37 || event.keyCode === 38) {
          scope.$apply(function () { accordionCtrl.focusPrevious(); });
          event.preventDefault();
        }
      }

      function onFocus () {
        paneCtrl.focusPane();
      }

      function onBlur () {
        paneCtrl.blurPane();
      }

      iElement[0].onfocus = onFocus;
      iElement[0].onblur = onBlur;
      iElement.bind('click', onClick);
      iElement.bind('keydown', onKeydown);

      scope.$on('$destroy', function () {
        iElement.unbind('click', onClick);
        iElement.unbind('keydown', onKeydown);
        iElement[0].onfocus = null;
        iElement[0].onblur = null;
      });
    }
  };
}

})(angular);
/*!
 * Copyright (c) 2016 Chris O'Hara <cohara87@gmail.com>
 * 
 * Permission is hereby granted, free of charge, to any person obtaining
 * a copy of this software and associated documentation files (the
 * "Software"), to deal in the Software without restriction, including
 * without limitation the rights to use, copy, modify, merge, publish,
 * distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to
 * the following conditions:
 * 
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 * MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
 * LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
 * WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
(function (global, factory) {
      typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
      typeof define === 'function' && define.amd ? define(factory) :
      (global.validator = factory());
}(this, function () { 'use strict';

      var babelHelpers = {};
      babelHelpers.typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
        return typeof obj;
      } : function (obj) {
        return obj && typeof Symbol === "function" && obj.constructor === Symbol ? "symbol" : typeof obj;
      };
      babelHelpers;

      function assertString(input) {
        if (typeof input !== 'string') {
          throw new TypeError('This library (validator.js) validates strings only');
        }
      }

      function toDate(date) {
        assertString(date);
        date = Date.parse(date);
        return !isNaN(date) ? new Date(date) : null;
      }

      function toFloat(str) {
        assertString(str);
        return parseFloat(str);
      }

      function toInt(str, radix) {
        assertString(str);
        return parseInt(str, radix || 10);
      }

      function toBoolean(str, strict) {
        assertString(str);
        if (strict) {
          return str === '1' || str === 'true';
        }
        return str !== '0' && str !== 'false' && str !== '';
      }

      function equals(str, comparison) {
        assertString(str);
        return str === comparison;
      }

      function toString(input) {
        if ((typeof input === 'undefined' ? 'undefined' : babelHelpers.typeof(input)) === 'object' && input !== null) {
          if (typeof input.toString === 'function') {
            input = input.toString();
          } else {
            input = '[object Object]';
          }
        } else if (input === null || typeof input === 'undefined' || isNaN(input) && !input.length) {
          input = '';
        }
        return String(input);
      }

      function contains(str, elem) {
        assertString(str);
        return str.indexOf(toString(elem)) >= 0;
      }

      function matches(str, pattern, modifiers) {
        assertString(str);
        if (Object.prototype.toString.call(pattern) !== '[object RegExp]') {
          pattern = new RegExp(pattern, modifiers);
        }
        return pattern.test(str);
      }

      function merge() {
        var obj = arguments.length <= 0 || arguments[0] === undefined ? {} : arguments[0];
        var defaults = arguments[1];

        for (var key in defaults) {
          if (typeof obj[key] === 'undefined') {
            obj[key] = defaults[key];
          }
        }
        return obj;
      }

      /* eslint-disable prefer-rest-params */
      function isByteLength(str, options) {
        assertString(str);
        var min = void 0;
        var max = void 0;
        if ((typeof options === 'undefined' ? 'undefined' : babelHelpers.typeof(options)) === 'object') {
          min = options.min || 0;
          max = options.max;
        } else {
          // backwards compatibility: isByteLength(str, min [, max])
          min = arguments[1];
          max = arguments[2];
        }
        var len = encodeURI(str).split(/%..|./).length - 1;
        return len >= min && (typeof max === 'undefined' || len <= max);
      }

      var default_fqdn_options = {
        require_tld: true,
        allow_underscores: false,
        allow_trailing_dot: false
      };

      function isFDQN(str, options) {
        assertString(str);
        options = merge(options, default_fqdn_options);

        /* Remove the optional trailing dot before checking validity */
        if (options.allow_trailing_dot && str[str.length - 1] === '.') {
          str = str.substring(0, str.length - 1);
        }
        var parts = str.split('.');
        if (options.require_tld) {
          var tld = parts.pop();
          if (!parts.length || !/^([a-z\u00a1-\uffff]{2,}|xn[a-z0-9-]{2,})$/i.test(tld)) {
            return false;
          }
        }
        for (var part, i = 0; i < parts.length; i++) {
          part = parts[i];
          if (options.allow_underscores) {
            part = part.replace(/_/g, '');
          }
          if (!/^[a-z\u00a1-\uffff0-9-]+$/i.test(part)) {
            return false;
          }
          if (/[\uff01-\uff5e]/.test(part)) {
            // disallow full-width chars
            return false;
          }
          if (part[0] === '-' || part[part.length - 1] === '-') {
            return false;
          }
        }
        return true;
      }

      var default_email_options = {
        allow_display_name: false,
        allow_utf8_local_part: true,
        require_tld: true
      };

      /* eslint-disable max-len */
      var displayName = /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~\.\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~\.\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF\s]*<(.+)>$/i;
      var emailUserPart = /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~]+$/i;
      var quotedEmailUser = /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f]))*$/i;
      var emailUserUtf8Part = /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+$/i;
      var quotedEmailUserUtf8 = /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*$/i;
      /* eslint-enable max-len */

      function isEmail(str, options) {
        assertString(str);
        options = merge(options, default_email_options);

        if (options.allow_display_name) {
          var display_email = str.match(displayName);
          if (display_email) {
            str = display_email[1];
          }
        }

        var parts = str.split('@');
        var domain = parts.pop();
        var user = parts.join('@');

        var lower_domain = domain.toLowerCase();
        if (lower_domain === 'gmail.com' || lower_domain === 'googlemail.com') {
          user = user.replace(/\./g, '').toLowerCase();
        }

        if (!isByteLength(user, { max: 64 }) || !isByteLength(domain, { max: 256 })) {
          return false;
        }

        if (!isFDQN(domain, { require_tld: options.require_tld })) {
          return false;
        }

        if (user[0] === '"') {
          user = user.slice(1, user.length - 1);
          return options.allow_utf8_local_part ? quotedEmailUserUtf8.test(user) : quotedEmailUser.test(user);
        }

        var pattern = options.allow_utf8_local_part ? emailUserUtf8Part : emailUserPart;

        var user_parts = user.split('.');
        for (var i = 0; i < user_parts.length; i++) {
          if (!pattern.test(user_parts[i])) {
            return false;
          }
        }

        return true;
      }

      var ipv4Maybe = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
      var ipv6Block = /^[0-9A-F]{1,4}$/i;

      function isIP(str) {
        var version = arguments.length <= 1 || arguments[1] === undefined ? '' : arguments[1];

        assertString(str);
        version = String(version);
        if (!version) {
          return isIP(str, 4) || isIP(str, 6);
        } else if (version === '4') {
          if (!ipv4Maybe.test(str)) {
            return false;
          }
          var parts = str.split('.').sort(function (a, b) {
            return a - b;
          });
          return parts[3] <= 255;
        } else if (version === '6') {
          var blocks = str.split(':');
          var foundOmissionBlock = false; // marker to indicate ::

          // At least some OS accept the last 32 bits of an IPv6 address
          // (i.e. 2 of the blocks) in IPv4 notation, and RFC 3493 says
          // that '::ffff:a.b.c.d' is valid for IPv4-mapped IPv6 addresses,
          // and '::a.b.c.d' is deprecated, but also valid.
          var foundIPv4TransitionBlock = isIP(blocks[blocks.length - 1], 4);
          var expectedNumberOfBlocks = foundIPv4TransitionBlock ? 7 : 8;

          if (blocks.length > expectedNumberOfBlocks) {
            return false;
          }
          // initial or final ::
          if (str === '::') {
            return true;
          } else if (str.substr(0, 2) === '::') {
            blocks.shift();
            blocks.shift();
            foundOmissionBlock = true;
          } else if (str.substr(str.length - 2) === '::') {
            blocks.pop();
            blocks.pop();
            foundOmissionBlock = true;
          }

          for (var i = 0; i < blocks.length; ++i) {
            // test for a :: which can not be at the string start/end
            // since those cases have been handled above
            if (blocks[i] === '' && i > 0 && i < blocks.length - 1) {
              if (foundOmissionBlock) {
                return false; // multiple :: in address
              }
              foundOmissionBlock = true;
            } else if (foundIPv4TransitionBlock && i === blocks.length - 1) {
              // it has been checked before that the last
              // block is a valid IPv4 address
            } else if (!ipv6Block.test(blocks[i])) {
                return false;
              }
          }
          if (foundOmissionBlock) {
            return blocks.length >= 1;
          }
          return blocks.length === expectedNumberOfBlocks;
        }
        return false;
      }

      var default_url_options = {
        protocols: ['http', 'https', 'ftp'],
        require_tld: true,
        require_protocol: false,
        require_valid_protocol: true,
        allow_underscores: false,
        allow_trailing_dot: false,
        allow_protocol_relative_urls: false
      };

      function isURL(url, options) {
        assertString(url);
        if (!url || url.length >= 2083 || /\s/.test(url)) {
          return false;
        }
        if (url.indexOf('mailto:') === 0) {
          return false;
        }
        options = merge(options, default_url_options);
        var protocol = void 0,
            auth = void 0,
            host = void 0,
            hostname = void 0,
            port = void 0,
            port_str = void 0,
            split = void 0;

        split = url.split('#');
        url = split.shift();

        split = url.split('?');
        url = split.shift();

        split = url.split('://');
        if (split.length > 1) {
          protocol = split.shift();
          if (options.require_valid_protocol && options.protocols.indexOf(protocol) === -1) {
            return false;
          }
        } else if (options.require_protocol) {
          return false;
        } else if (options.allow_protocol_relative_urls && url.substr(0, 2) === '//') {
          split[0] = url.substr(2);
        }
        url = split.join('://');

        split = url.split('/');
        url = split.shift();
        split = url.split('@');
        if (split.length > 1) {
          auth = split.shift();
          if (auth.indexOf(':') >= 0 && auth.split(':').length > 2) {
            return false;
          }
        }
        hostname = split.join('@');
        split = hostname.split(':');
        host = split.shift();
        if (split.length) {
          port_str = split.join(':');
          port = parseInt(port_str, 10);
          if (!/^[0-9]+$/.test(port_str) || port <= 0 || port > 65535) {
            return false;
          }
        }
        if (!isIP(host) && !isFDQN(host, options) && host !== 'localhost') {
          return false;
        }
        if (options.host_whitelist && options.host_whitelist.indexOf(host) === -1) {
          return false;
        }
        if (options.host_blacklist && options.host_blacklist.indexOf(host) !== -1) {
          return false;
        }
        return true;
      }

      var macAddress = /^([0-9a-fA-F][0-9a-fA-F]:){5}([0-9a-fA-F][0-9a-fA-F])$/;

      function isMACAddress(str) {
        assertString(str);
        return macAddress.test(str);
      }

      function isBoolean(str) {
        assertString(str);
        return ['true', 'false', '1', '0'].indexOf(str) >= 0;
      }

      var alpha = {
        'en-US': /^[A-Z]+$/i,
        'cs-CZ': /^[A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]+$/i,
        'de-DE': /^[A-ZÄÖÜß]+$/i,
        'es-ES': /^[A-ZÁÉÍÑÓÚÜ]+$/i,
        'fr-FR': /^[A-ZÀÂÆÇÉÈÊËÏÎÔŒÙÛÜŸ]+$/i,
        'nl-NL': /^[A-ZÉËÏÓÖÜ]+$/i,
        'pl-PL': /^[A-ZĄĆĘŚŁŃÓŻŹ]+$/i,
        'pt-PT': /^[A-ZÃÁÀÂÇÉÊÍÕÓÔÚÜ]+$/i,
        'ru-RU': /^[А-ЯЁа-яё]+$/i,
        'tr-TR': /^[A-ZÇĞİıÖŞÜ]+$/i,
        ar: /^[ءآأؤإئابةتثجحخدذرزسشصضطظعغفقكلمنهوىيًٌٍَُِّْٰ]+$/
      };

      var alphanumeric = {
        'en-US': /^[0-9A-Z]+$/i,
        'cs-CZ': /^[0-9A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]+$/i,
        'de-DE': /^[0-9A-ZÄÖÜß]+$/i,
        'es-ES': /^[0-9A-ZÁÉÍÑÓÚÜ]+$/i,
        'fr-FR': /^[0-9A-ZÀÂÆÇÉÈÊËÏÎÔŒÙÛÜŸ]+$/i,
        'nl-NL': /^[0-9A-ZÉËÏÓÖÜ]+$/i,
        'pl-PL': /^[0-9A-ZĄĆĘŚŁŃÓŻŹ]+$/i,
        'pt-PT': /^[0-9A-ZÃÁÀÂÇÉÊÍÕÓÔÚÜ]+$/i,
        'ru-RU': /^[0-9А-ЯЁа-яё]+$/i,
        'tr-TR': /^[0-9A-ZÇĞİıÖŞÜ]+$/i,
        ar: /^[٠١٢٣٤٥٦٧٨٩0-9ءآأؤإئابةتثجحخدذرزسشصضطظعغفقكلمنهوىيًٌٍَُِّْٰ]+$/
      };

      var englishLocales = ['AU', 'GB', 'HK', 'IN', 'NZ', 'ZA', 'ZM'];

      for (var locale, i = 0; i < englishLocales.length; i++) {
        locale = 'en-' + englishLocales[i];
        alpha[locale] = alpha['en-US'];
        alphanumeric[locale] = alphanumeric['en-US'];
      }

      // Source: http://www.localeplanet.com/java/
      var arabicLocales = ['AE', 'BH', 'DZ', 'EG', 'IQ', 'JO', 'KW', 'LB', 'LY', 'MA', 'QM', 'QA', 'SA', 'SD', 'SY', 'TN', 'YE'];

      for (var _locale, _i = 0; _i < arabicLocales.length; _i++) {
        _locale = 'ar-' + arabicLocales[_i];
        alpha[_locale] = alpha.ar;
        alphanumeric[_locale] = alphanumeric.ar;
      }

      function isAlpha(str) {
        var locale = arguments.length <= 1 || arguments[1] === undefined ? 'en-US' : arguments[1];

        assertString(str);
        if (locale in alpha) {
          return alpha[locale].test(str);
        }
        throw new Error('Invalid locale \'' + locale + '\'');
      }

      function isAlphanumeric(str) {
        var locale = arguments.length <= 1 || arguments[1] === undefined ? 'en-US' : arguments[1];

        assertString(str);
        if (locale in alphanumeric) {
          return alphanumeric[locale].test(str);
        }
        throw new Error('Invalid locale \'' + locale + '\'');
      }

      var numeric = /^[-+]?[0-9]+$/;

      function isNumeric(str) {
        assertString(str);
        return numeric.test(str);
      }

      function isLowercase(str) {
        assertString(str);
        return str === str.toLowerCase();
      }

      function isUppercase(str) {
        assertString(str);
        return str === str.toUpperCase();
      }

      var ascii = /^[\x00-\x7F]+$/;

      function isAscii(str) {
        assertString(str);
        return ascii.test(str);
      }

      var fullWidth = /[^\u0020-\u007E\uFF61-\uFF9F\uFFA0-\uFFDC\uFFE8-\uFFEE0-9a-zA-Z]/;

      function isFullWidth(str) {
        assertString(str);
        return fullWidth.test(str);
      }

      var halfWidth = /[\u0020-\u007E\uFF61-\uFF9F\uFFA0-\uFFDC\uFFE8-\uFFEE0-9a-zA-Z]/;

      function isHalfWidth(str) {
        assertString(str);
        return halfWidth.test(str);
      }

      function isVariableWidth(str) {
        assertString(str);
        return fullWidth.test(str) && halfWidth.test(str);
      }

      var multibyte = /[^\x00-\x7F]/;

      function isMultibyte(str) {
        assertString(str);
        return multibyte.test(str);
      }

      var surrogatePair = /[\uD800-\uDBFF][\uDC00-\uDFFF]/;

      function isSurrogatePair(str) {
        assertString(str);
        return surrogatePair.test(str);
      }

      var int = /^(?:[-+]?(?:0|[1-9][0-9]*))$/;

      function isInt(str, options) {
        assertString(str);
        options = options || {};
        return int.test(str) && (!options.hasOwnProperty('min') || str >= options.min) && (!options.hasOwnProperty('max') || str <= options.max);
      }

      var float = /^(?:[-+]?(?:[0-9]+))?(?:\.[0-9]*)?(?:[eE][\+\-]?(?:[0-9]+))?$/;

      function isFloat(str, options) {
        assertString(str);
        options = options || {};
        if (str === '' || str === '.') {
          return false;
        }
        return float.test(str) && (!options.hasOwnProperty('min') || str >= options.min) && (!options.hasOwnProperty('max') || str <= options.max);
      }

      var decimal = /^[-+]?([0-9]+|\.[0-9]+|[0-9]+\.[0-9]+)$/;

      function isDecimal(str) {
        assertString(str);
        return str !== '' && decimal.test(str);
      }

      var hexadecimal = /^[0-9A-F]+$/i;

      function isHexadecimal(str) {
        assertString(str);
        return hexadecimal.test(str);
      }

      function isDivisibleBy(str, num) {
        assertString(str);
        return toFloat(str) % parseInt(num, 10) === 0;
      }

      var hexcolor = /^#?([0-9A-F]{3}|[0-9A-F]{6})$/i;

      function isHexColor(str) {
        assertString(str);
        return hexcolor.test(str);
      }

      function isJSON(str) {
        assertString(str);
        try {
          var obj = JSON.parse(str);
          return !!obj && (typeof obj === 'undefined' ? 'undefined' : babelHelpers.typeof(obj)) === 'object';
        } catch (e) {/* ignore */}
        return false;
      }

      function isNull(str) {
        assertString(str);
        return str.length === 0;
      }

      /* eslint-disable prefer-rest-params */
      function isLength(str, options) {
        assertString(str);
        var min = void 0;
        var max = void 0;
        if ((typeof options === 'undefined' ? 'undefined' : babelHelpers.typeof(options)) === 'object') {
          min = options.min || 0;
          max = options.max;
        } else {
          // backwards compatibility: isLength(str, min [, max])
          min = arguments[1];
          max = arguments[2];
        }
        var surrogatePairs = str.match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g) || [];
        var len = str.length - surrogatePairs.length;
        return len >= min && (typeof max === 'undefined' || len <= max);
      }

      var uuid = {
        3: /^[0-9A-F]{8}-[0-9A-F]{4}-3[0-9A-F]{3}-[0-9A-F]{4}-[0-9A-F]{12}$/i,
        4: /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i,
        5: /^[0-9A-F]{8}-[0-9A-F]{4}-5[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i,
        all: /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i
      };

      function isUUID(str) {
        var version = arguments.length <= 1 || arguments[1] === undefined ? 'all' : arguments[1];

        assertString(str);
        var pattern = uuid[version];
        return pattern && pattern.test(str);
      }

      function isMongoId(str) {
        assertString(str);
        return isHexadecimal(str) && str.length === 24;
      }

      /* eslint-disable max-len */
      // from http://goo.gl/0ejHHW
      var iso8601 = /^([\+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24\:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;
      /* eslint-enable max-len */

      function isISO8601 (str) {
        assertString(str);
        return iso8601.test(str);
      }

      function getTimezoneOffset(str) {
        var iso8601Parts = str.match(iso8601);
        var timezone = void 0,
            sign = void 0,
            hours = void 0,
            minutes = void 0;
        if (!iso8601Parts) {
          str = str.toLowerCase();
          timezone = str.match(/(?:\s|gmt\s*)(-|\+)(\d{1,4})(\s|$)/);
          if (!timezone) {
            return str.indexOf('gmt') !== -1 ? 0 : null;
          }
          sign = timezone[1];
          var offset = timezone[2];
          if (offset.length === 3) {
            offset = '0' + offset;
          }
          if (offset.length <= 2) {
            hours = 0;
            minutes = parseInt(offset, 10);
          } else {
            hours = parseInt(offset.slice(0, 2), 10);
            minutes = parseInt(offset.slice(2, 4), 10);
          }
        } else {
          timezone = iso8601Parts[21];
          if (!timezone) {
            // if no hour/minute was provided, the date is GMT
            return !iso8601Parts[12] ? 0 : null;
          }
          if (timezone === 'z' || timezone === 'Z') {
            return 0;
          }
          sign = iso8601Parts[22];
          if (timezone.indexOf(':') !== -1) {
            hours = parseInt(iso8601Parts[23], 10);
            minutes = parseInt(iso8601Parts[24], 10);
          } else {
            hours = 0;
            minutes = parseInt(iso8601Parts[23], 10);
          }
        }
        return (hours * 60 + minutes) * (sign === '-' ? 1 : -1);
      }

      function isDate(str) {
        assertString(str);
        var normalizedDate = new Date(Date.parse(str));
        if (isNaN(normalizedDate)) {
          return false;
        }

        // normalizedDate is in the user's timezone. Apply the input
        // timezone offset to the date so that the year and day match
        // the input
        var timezoneOffset = getTimezoneOffset(str);
        if (timezoneOffset !== null) {
          var timezoneDifference = normalizedDate.getTimezoneOffset() - timezoneOffset;
          normalizedDate = new Date(normalizedDate.getTime() + 60000 * timezoneDifference);
        }

        var day = String(normalizedDate.getDate());
        var dayOrYear = void 0,
            dayOrYearMatches = void 0,
            year = void 0;
        // check for valid double digits that could be late days
        // check for all matches since a string like '12/23' is a valid date
        // ignore everything with nearby colons
        dayOrYearMatches = str.match(/(^|[^:\d])[23]\d([^:\d]|$)/g);
        if (!dayOrYearMatches) {
          return true;
        }
        dayOrYear = dayOrYearMatches.map(function (digitString) {
          return digitString.match(/\d+/g)[0];
        }).join('/');

        year = String(normalizedDate.getFullYear()).slice(-2);
        if (dayOrYear === day || dayOrYear === year) {
          return true;
        } else if (dayOrYear === '' + day / year || dayOrYear === '' + year / day) {
          return true;
        }
        return false;
      }

      function isAfter(str) {
        var date = arguments.length <= 1 || arguments[1] === undefined ? String(new Date()) : arguments[1];

        assertString(str);
        var comparison = toDate(date);
        var original = toDate(str);
        return !!(original && comparison && original > comparison);
      }

      function isBefore(str) {
        var date = arguments.length <= 1 || arguments[1] === undefined ? String(new Date()) : arguments[1];

        assertString(str);
        var comparison = toDate(date);
        var original = toDate(str);
        return !!(original && comparison && original < comparison);
      }

      function isIn(str, options) {
        assertString(str);
        var i = void 0;
        if (Object.prototype.toString.call(options) === '[object Array]') {
          var array = [];
          for (i in options) {
            if ({}.hasOwnProperty.call(options, i)) {
              array[i] = toString(options[i]);
            }
          }
          return array.indexOf(str) >= 0;
        } else if ((typeof options === 'undefined' ? 'undefined' : babelHelpers.typeof(options)) === 'object') {
          return options.hasOwnProperty(str);
        } else if (options && typeof options.indexOf === 'function') {
          return options.indexOf(str) >= 0;
        }
        return false;
      }

      /* eslint-disable max-len */
      var creditCard = /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/;
      /* eslint-enable max-len */

      function isCreditCard(str) {
        assertString(str);
        var sanitized = str.replace(/[^0-9]+/g, '');
        if (!creditCard.test(sanitized)) {
          return false;
        }
        var sum = 0;
        var digit = void 0;
        var tmpNum = void 0;
        var shouldDouble = void 0;
        for (var i = sanitized.length - 1; i >= 0; i--) {
          digit = sanitized.substring(i, i + 1);
          tmpNum = parseInt(digit, 10);
          if (shouldDouble) {
            tmpNum *= 2;
            if (tmpNum >= 10) {
              sum += tmpNum % 10 + 1;
            } else {
              sum += tmpNum;
            }
          } else {
            sum += tmpNum;
          }
          shouldDouble = !shouldDouble;
        }
        return !!(sum % 10 === 0 ? sanitized : false);
      }

      var isin = /^[A-Z]{2}[0-9A-Z]{9}[0-9]$/;

      function isISIN(str) {
        assertString(str);
        if (!isin.test(str)) {
          return false;
        }

        var checksumStr = str.replace(/[A-Z]/g, function (character) {
          return parseInt(character, 36);
        });

        var sum = 0;
        var digit = void 0;
        var tmpNum = void 0;
        var shouldDouble = true;
        for (var i = checksumStr.length - 2; i >= 0; i--) {
          digit = checksumStr.substring(i, i + 1);
          tmpNum = parseInt(digit, 10);
          if (shouldDouble) {
            tmpNum *= 2;
            if (tmpNum >= 10) {
              sum += tmpNum + 1;
            } else {
              sum += tmpNum;
            }
          } else {
            sum += tmpNum;
          }
          shouldDouble = !shouldDouble;
        }

        return parseInt(str.substr(str.length - 1), 10) === (10000 - sum) % 10;
      }

      var isbn10Maybe = /^(?:[0-9]{9}X|[0-9]{10})$/;
      var isbn13Maybe = /^(?:[0-9]{13})$/;
      var factor = [1, 3];

      function isISBN(str) {
        var version = arguments.length <= 1 || arguments[1] === undefined ? '' : arguments[1];

        assertString(str);
        version = String(version);
        if (!version) {
          return isISBN(str, 10) || isISBN(str, 13);
        }
        var sanitized = str.replace(/[\s-]+/g, '');
        var checksum = 0;
        var i = void 0;
        if (version === '10') {
          if (!isbn10Maybe.test(sanitized)) {
            return false;
          }
          for (i = 0; i < 9; i++) {
            checksum += (i + 1) * sanitized.charAt(i);
          }
          if (sanitized.charAt(9) === 'X') {
            checksum += 10 * 10;
          } else {
            checksum += 10 * sanitized.charAt(9);
          }
          if (checksum % 11 === 0) {
            return !!sanitized;
          }
        } else if (version === '13') {
          if (!isbn13Maybe.test(sanitized)) {
            return false;
          }
          for (i = 0; i < 12; i++) {
            checksum += factor[i % 2] * sanitized.charAt(i);
          }
          if (sanitized.charAt(12) - (10 - checksum % 10) % 10 === 0) {
            return !!sanitized;
          }
        }
        return false;
      }

      /* eslint-disable max-len */
      var phones = {
        'ar-SY': /^(!?(\+?963)|0)?9\d{8}$/,
        'en-US': /^(\+?1)?[2-9]\d{2}[2-9](?!11)\d{6}$/,
        'cs-CZ': /^(\+?420)? ?[1-9][0-9]{2} ?[0-9]{3} ?[0-9]{3}$/,
        'de-DE': /^(\+?49[ \.\-])?([\(]{1}[0-9]{1,6}[\)])?([0-9 \.\-\/]{3,20})((x|ext|extension)[ ]?[0-9]{1,4})?$/,
        'el-GR': /^(\+?30)?(69\d{8})$/,
        'en-AU': /^(\+?61|0)4\d{8}$/,
        'en-GB': /^(\+?44|0)7\d{9}$/,
        'en-HK': /^(\+?852\-?)?[569]\d{3}\-?\d{4}$/,
        'en-IN': /^(\+?91|0)?[789]\d{9}$/,
        'en-NZ': /^(\+?64|0)2\d{7,9}$/,
        'en-ZA': /^(\+?27|0)\d{9}$/,
        'en-ZM': /^(\+?26)?09[567]\d{7}$/,
        'es-ES': /^(\+?34)?(6\d{1}|7[1234])\d{7}$/,
        'fi-FI': /^(\+?358|0)\s?(4(0|1|2|4|5)?|50)\s?(\d\s?){4,8}\d$/,
        'fr-FR': /^(\+?33|0)[67]\d{8}$/,
        'ms-MY': /^(\+?6?01){1}(([145]{1}(\-|\s)?\d{7,8})|([236789]{1}(\s|\-)?\d{7}))$/,
        'nb-NO': /^(\+?47)?[49]\d{7}$/,
        'nn-NO': /^(\+?47)?[49]\d{7}$/,
        'pt-BR': /^(\+?55|0)\-?[1-9]{2}\-?[2-9]{1}\d{3,4}\-?\d{4}$/,
        'pt-PT': /^(\+?351)?9[1236]\d{7}$/,
        'ru-RU': /^(\+?7|8)?9\d{9}$/,
        'tr-TR': /^(\+?90|0)?5\d{9}$/,
        'vi-VN': /^(\+?84|0)?((1(2([0-9])|6([2-9])|88|99))|(9((?!5)[0-9])))([0-9]{7})$/,
        'zh-CN': /^(\+?0?86\-?)?((13\d|14[57]|15[^4,\D]|17[678]|18\d)\d{8}|170[059]\d{7})$/,
        'zh-TW': /^(\+?886\-?|0)?9\d{8}$/
      };
      /* eslint-enable max-len */

      function isMobilePhone(str, locale) {
        assertString(str);
        if (locale in phones) {
          return phones[locale].test(str);
        }
        return false;
      }

      function currencyRegex(options) {
        var symbol = '(\\' + options.symbol.replace(/\./g, '\\.') + ')' + (options.require_symbol ? '' : '?'),
            negative = '-?',
            whole_dollar_amount_without_sep = '[1-9]\\d*',
            whole_dollar_amount_with_sep = '[1-9]\\d{0,2}(\\' + options.thousands_separator + '\\d{3})*',
            valid_whole_dollar_amounts = ['0', whole_dollar_amount_without_sep, whole_dollar_amount_with_sep],
            whole_dollar_amount = '(' + valid_whole_dollar_amounts.join('|') + ')?',
            decimal_amount = '(\\' + options.decimal_separator + '\\d{2})?';
        var pattern = whole_dollar_amount + decimal_amount;

        // default is negative sign before symbol, but there are two other options (besides parens)
        if (options.allow_negatives && !options.parens_for_negatives) {
          if (options.negative_sign_after_digits) {
            pattern += negative;
          } else if (options.negative_sign_before_digits) {
            pattern = negative + pattern;
          }
        }

        // South African Rand, for example, uses R 123 (space) and R-123 (no space)
        if (options.allow_negative_sign_placeholder) {
          pattern = '( (?!\\-))?' + pattern;
        } else if (options.allow_space_after_symbol) {
          pattern = ' ?' + pattern;
        } else if (options.allow_space_after_digits) {
          pattern += '( (?!$))?';
        }

        if (options.symbol_after_digits) {
          pattern += symbol;
        } else {
          pattern = symbol + pattern;
        }

        if (options.allow_negatives) {
          if (options.parens_for_negatives) {
            pattern = '(\\(' + pattern + '\\)|' + pattern + ')';
          } else if (!(options.negative_sign_before_digits || options.negative_sign_after_digits)) {
            pattern = negative + pattern;
          }
        }

        /* eslint-disable prefer-template */
        return new RegExp('^' +
        // ensure there's a dollar and/or decimal amount, and that
        // it doesn't start with a space or a negative sign followed by a space
        '(?!-? )(?=.*\\d)' + pattern + '$');
        /* eslint-enable prefer-template */
      }

      var default_currency_options = {
        symbol: '$',
        require_symbol: false,
        allow_space_after_symbol: false,
        symbol_after_digits: false,
        allow_negatives: true,
        parens_for_negatives: false,
        negative_sign_before_digits: false,
        negative_sign_after_digits: false,
        allow_negative_sign_placeholder: false,
        thousands_separator: ',',
        decimal_separator: '.',
        allow_space_after_digits: false
      };

      function isCurrency(str, options) {
        assertString(str);
        options = merge(options, default_currency_options);
        return currencyRegex(options).test(str);
      }

      var notBase64 = /[^A-Z0-9+\/=]/i;

      function isBase64(str) {
        assertString(str);
        var len = str.length;
        if (!len || len % 4 !== 0 || notBase64.test(str)) {
          return false;
        }
        var firstPaddingChar = str.indexOf('=');
        return firstPaddingChar === -1 || firstPaddingChar === len - 1 || firstPaddingChar === len - 2 && str[len - 1] === '=';
      }

      var dataURI = /^\s*data:([a-z]+\/[a-z0-9\-\+]+(;[a-z\-]+\=[a-z0-9\-]+)?)?(;base64)?,[a-z0-9\!\$\&\'\,\(\)\*\+\,\;\=\-\.\_\~\:\@\/\?\%\s]*\s*$/i; // eslint-disable-line max-len

      function isDataURI(str) {
        assertString(str);
        return dataURI.test(str);
      }

      function ltrim(str, chars) {
        assertString(str);
        var pattern = chars ? new RegExp('^[' + chars + ']+', 'g') : /^\s+/g;
        return str.replace(pattern, '');
      }

      function rtrim(str, chars) {
        assertString(str);
        var pattern = chars ? new RegExp('[' + chars + ']+$', 'g') : /\s+$/g;
        return str.replace(pattern, '');
      }

      function trim(str, chars) {
        assertString(str);
        var pattern = chars ? new RegExp('^[' + chars + ']+|[' + chars + ']+$', 'g') : /^\s+|\s+$/g;
        return str.replace(pattern, '');
      }

      function escape(str) {
            assertString(str);
            return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\//g, '&#x2F;').replace(/\`/g, '&#96;');
      }

      function unescape(str) {
            assertString(str);
            return str.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#x27;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#x2F;/g, '\/').replace(/&#96;/g, '\`');
      }

      function blacklist(str, chars) {
        assertString(str);
        return str.replace(new RegExp('[' + chars + ']+', 'g'), '');
      }

      function stripLow(str, keep_new_lines) {
        assertString(str);
        var chars = keep_new_lines ? '\\x00-\\x09\\x0B\\x0C\\x0E-\\x1F\\x7F' : '\\x00-\\x1F\\x7F';
        return blacklist(str, chars);
      }

      function whitelist(str, chars) {
        assertString(str);
        return str.replace(new RegExp('[^' + chars + ']+', 'g'), '');
      }

      function isWhitelisted(str, chars) {
        assertString(str);
        for (var i = str.length - 1; i >= 0; i--) {
          if (chars.indexOf(str[i]) === -1) {
            return false;
          }
        }
        return true;
      }

      var default_normalize_email_options = {
        lowercase: true,
        remove_dots: true,
        remove_extension: true
      };

      function normalizeEmail(email, options) {
        options = merge(options, default_normalize_email_options);
        if (!isEmail(email)) {
          return false;
        }
        var parts = email.split('@', 2);
        parts[1] = parts[1].toLowerCase();
        if (parts[1] === 'gmail.com' || parts[1] === 'googlemail.com') {
          if (options.remove_extension) {
            parts[0] = parts[0].split('+')[0];
          }
          if (options.remove_dots) {
            parts[0] = parts[0].replace(/\./g, '');
          }
          if (!parts[0].length) {
            return false;
          }
          parts[0] = parts[0].toLowerCase();
          parts[1] = 'gmail.com';
        } else if (options.lowercase) {
          parts[0] = parts[0].toLowerCase();
        }
        return parts.join('@');
      }

      var version = '5.2.0';

      var validator = {
        version: version,
        toDate: toDate,
        toFloat: toFloat, toInt: toInt,
        toBoolean: toBoolean,
        equals: equals, contains: contains, matches: matches,
        isEmail: isEmail, isURL: isURL, isMACAddress: isMACAddress, isIP: isIP, isFQDN: isFDQN,
        isBoolean: isBoolean,
        isAlpha: isAlpha, isAlphanumeric: isAlphanumeric, isNumeric: isNumeric, isLowercase: isLowercase, isUppercase: isUppercase,
        isAscii: isAscii, isFullWidth: isFullWidth, isHalfWidth: isHalfWidth, isVariableWidth: isVariableWidth,
        isMultibyte: isMultibyte, isSurrogatePair: isSurrogatePair,
        isInt: isInt, isFloat: isFloat, isDecimal: isDecimal, isHexadecimal: isHexadecimal, isDivisibleBy: isDivisibleBy,
        isHexColor: isHexColor,
        isJSON: isJSON,
        isNull: isNull,
        isLength: isLength, isByteLength: isByteLength,
        isUUID: isUUID, isMongoId: isMongoId,
        isDate: isDate, isAfter: isAfter, isBefore: isBefore,
        isIn: isIn,
        isCreditCard: isCreditCard,
        isISIN: isISIN, isISBN: isISBN,
        isMobilePhone: isMobilePhone,
        isCurrency: isCurrency,
        isISO8601: isISO8601,
        isBase64: isBase64, isDataURI: isDataURI,
        ltrim: ltrim, rtrim: rtrim, trim: trim,
        escape: escape, unescape: unescape, stripLow: stripLow,
        whitelist: whitelist, blacklist: blacklist,
        isWhitelisted: isWhitelisted,
        normalizeEmail: normalizeEmail,
        toString: toString
      };

      return validator;

}));
/*
@license textAngular
Author : Austin Anderson
License : 2013 MIT
Version 1.5.16

See README.md or https://github.com/fraywing/textAngular/wiki for requirements and use.
*/

/*
Commonjs package manager support (eg componentjs).
*/


"use strict";// NOTE: textAngularVersion must match the Gruntfile.js 'setVersion' task.... and have format v/d+./d+./d+
var textAngularVersion = 'v1.5.16';   // This is automatically updated during the build process to the current release!


// IE version detection - http://stackoverflow.com/questions/4169160/javascript-ie-detection-why-not-use-simple-conditional-comments
// We need this as IE sometimes plays funny tricks with the contenteditable.
// ----------------------------------------------------------
// If you're not in IE (or IE version is less than 5) then:
// ie === undefined
// If you're in IE (>=5) then you can determine which version:
// ie === 7; // IE7
// Thus, to detect IE:
// if (ie) {}
// And to detect the version:
// ie === 6 // IE6
// ie > 7 // IE8, IE9, IE10 ...
// ie < 9 // Anything less than IE9
// ----------------------------------------------------------
/* istanbul ignore next: untestable browser check */
var _browserDetect = {
	ie: (function(){
		var undef,
			v = 3,
			div = document.createElement('div'),
			all = div.getElementsByTagName('i');

		while (
			div.innerHTML = '<!--[if gt IE ' + (++v) + ']><i></i><![endif]-->',
			all[0]
		);

		return v > 4 ? v : undef;
	}()),
	webkit: /AppleWebKit\/([\d.]+)/i.test(navigator.userAgent),
	isFirefox: navigator.userAgent.toLowerCase().indexOf('firefox') > -1
};

// Global to textAngular to measure performance where needed
/* istanbul ignore next: untestable browser check */
var performance = performance || {};
/* istanbul ignore next: untestable browser check */
performance.now = (function() {
	return performance.now       ||
		performance.mozNow    ||
		performance.msNow     ||
		performance.oNow      ||
		performance.webkitNow ||
		function() { return new Date().getTime(); };
})();
// usage is:
// var t0 = performance.now();
// doSomething();
// var t1 = performance.now();
// console.log('Took', (t1 - t0).toFixed(4), 'milliseconds to do something!');
//

// turn html into pure text that shows visiblity
function stripHtmlToText(html)
{
	var tmp = document.createElement("DIV");
	tmp.innerHTML = html;
	var res = tmp.textContent || tmp.innerText || '';
	res.replace('\u200B', ''); // zero width space
	res = res.trim();
	return res;
}
// get html
function getDomFromHtml(html)
{
	var tmp = document.createElement("DIV");
	tmp.innerHTML = html;
	return tmp;
}


// Global to textAngular REGEXP vars for block and list elements.

var BLOCKELEMENTS = /^(address|article|aside|audio|blockquote|canvas|center|dd|div|dl|fieldset|figcaption|figure|footer|form|h1|h2|h3|h4|h5|h6|header|hgroup|hr|noscript|ol|output|p|pre|section|table|tfoot|ul|video)$/i;
var LISTELEMENTS = /^(ul|li|ol)$/i;
// updated VALIDELEMENTS to include #text and span so that we can use nodeName instead of tagName
var VALIDELEMENTS = /^(#text|span|address|article|aside|audio|blockquote|canvas|center|dd|div|dl|fieldset|figcaption|figure|footer|form|h1|h2|h3|h4|h5|h6|header|hgroup|hr|noscript|ol|output|p|pre|section|table|tfoot|ul|video|li)$/i;


// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/Trim#Compatibility
/* istanbul ignore next: trim shim for older browsers */
if (!String.prototype.trim) {
	String.prototype.trim = function () {
		return this.replace(/^\s+|\s+$/g, '');
	};
}

/*
	Custom stylesheet for the placeholders rules.
	Credit to: http://davidwalsh.name/add-rules-stylesheets
*/
var sheet, addCSSRule, removeCSSRule, _addCSSRule, _removeCSSRule, _getRuleIndex;
/* istanbul ignore else: IE <8 test*/
if(_browserDetect.ie > 8 || _browserDetect.ie === undefined){
	var _sheets = document.styleSheets;
	/* istanbul ignore next: preference for stylesheet loaded externally */
	for(var i = 0; i < _sheets.length; i++){
		if(_sheets[i].media.length === 0 || _sheets[i].media.mediaText.match(/(all|screen)/ig)){
			if(_sheets[i].href){
				if(_sheets[i].href.match(/textangular\.(min\.|)css/ig)){
					sheet = _sheets[i];
					break;
				}
			}
		}
	}
	/* istanbul ignore next: preference for stylesheet loaded externally */
	if(!sheet){
		// this sheet is used for the placeholders later on.
		sheet = (function() {
			// Create the <style> tag
			var style = document.createElement("style");
			/* istanbul ignore else : WebKit hack :( */
			if(_browserDetect.webkit) style.appendChild(document.createTextNode(""));

			// Add the <style> element to the page, add as first so the styles can be overridden by custom stylesheets
			document.getElementsByTagName('head')[0].appendChild(style);

			return style.sheet;
		})();
	}

	// use as: addCSSRule("header", "float: left");
	addCSSRule = function(selector, rules) {
		return _addCSSRule(sheet, selector, rules);
	};
	_addCSSRule = function(_sheet, selector, rules){
		var insertIndex;
		var insertedRule;
		// This order is important as IE 11 has both cssRules and rules but they have different lengths - cssRules is correct, rules gives an error in IE 11
		/* istanbul ignore next: browser catches */
		if(_sheet.cssRules) insertIndex = Math.max(_sheet.cssRules.length - 1, 0);
		else if(_sheet.rules) insertIndex = Math.max(_sheet.rules.length - 1, 0);

		/* istanbul ignore else: untestable IE option */
		if(_sheet.insertRule) {
			_sheet.insertRule(selector + "{" + rules + "}", insertIndex);
		}
		else {
			_sheet.addRule(selector, rules, insertIndex);
		}
		/* istanbul ignore next: browser catches */
		if(sheet.rules) insertedRule = sheet.rules[insertIndex];
		else if(sheet.cssRules) insertedRule = sheet.cssRules[insertIndex];
		// return the inserted stylesheet rule
		return insertedRule;
	};

	_getRuleIndex = function(rule, rules) {
		var i, ruleIndex;
		for (i=0; i < rules.length; i++) {
			/* istanbul ignore else: check for correct rule */
			if (rules[i].cssText === rule.cssText) {
				ruleIndex = i;
				break;
			}
		}
		return ruleIndex;
	};

	removeCSSRule = function(rule){
		_removeCSSRule(sheet, rule);
	};
	/* istanbul ignore next: tests are browser specific */
	_removeCSSRule = function(sheet, rule){
		var rules = sheet.cssRules || sheet.rules;
		if(!rules || rules.length === 0) return;
		var ruleIndex = _getRuleIndex(rule, rules);
		if(sheet.removeRule){
			sheet.removeRule(ruleIndex);
		}else{
			sheet.deleteRule(ruleIndex);
		}
	};
}

angular.module('textAngular.factories', [])
.factory('taBrowserTag', [function(){
    return function(tag){
        /* istanbul ignore next: ie specific test */
        if(!tag) return (_browserDetect.ie <= 8)? 'P' : 'p';
        else if(tag === '') return (_browserDetect.ie === undefined)? 'div' : (_browserDetect.ie <= 8)? 'P' : 'p';
        else return (_browserDetect.ie <= 8)? tag.toUpperCase() : tag;
    };
}]).factory('taApplyCustomRenderers', ['taCustomRenderers', 'taDOM', function(taCustomRenderers, taDOM){
    return function(val){
        var element = angular.element('<div></div>');
        element[0].innerHTML = val;

        angular.forEach(taCustomRenderers, function(renderer){
            var elements = [];
            // get elements based on what is defined. If both defined do secondary filter in the forEach after using selector string
            if(renderer.selector && renderer.selector !== '')
                elements = element.find(renderer.selector);
            /* istanbul ignore else: shouldn't fire, if it does we're ignoring everything */
            else if(renderer.customAttribute && renderer.customAttribute !== '')
                elements = taDOM.getByAttribute(element, renderer.customAttribute);
            // process elements if any found
            angular.forEach(elements, function(_element){
                _element = angular.element(_element);
                if(renderer.selector && renderer.selector !== '' && renderer.customAttribute && renderer.customAttribute !== ''){
                    if(_element.attr(renderer.customAttribute) !== undefined) renderer.renderLogic(_element);
                } else renderer.renderLogic(_element);
            });
        });

        return element[0].innerHTML;
    };
}]).factory('taFixChrome', function(){
    // get whaterever rubbish is inserted in chrome
    // should be passed an html string, returns an html string
    var taFixChrome = function(html, keepStyles){
        if(!html || !angular.isString(html) || html.length <= 0) return html;
        // grab all elements with a style attibute
        // a betterSpanMatch matches only a style=... with matching quotes
        // this captures the whole:
        // 'style="background-color: rgb(255, 255, 255);"'
        var betterSpanMatch = /style\s?=\s?(["'])(?:(?=(\\?))\2.)*?\1/ig;
        // where the original spanMatch = /<([^>\/]+?)style=("([^\"]+)"|'([^']+)')([^>]*)>/ig;
        // captures too much and includes the front tag!
        var spanMatch = /<([^>\/]+?)style=("([^\"]+)"|'([^']+)')([^>]*)>/ig;
        var appleConvertedSpaceMatch = /<span class="Apple-converted-space">([^<]+)<\/span>/ig;
        var match, styleVal, appleSpaceVal, newTag, finalHtml = '', lastIndex = 0;
        // remove all the Apple-converted-space spans and replace with the content of the span
        //console.log('before:', html);
        /* istanbul ignore next: apple-contereted-space span match */
        while(match = appleConvertedSpaceMatch.exec(html)){
            appleSpaceVal = match[1];
            appleSpaceVal = appleSpaceVal.replace(/&nbsp;/ig, ' ');
            finalHtml += html.substring(lastIndex, match.index) + appleSpaceVal;
            lastIndex = match.index + match[0].length;
        }
        /* istanbul ignore next: apple-contereted-space span has matched */
        if (lastIndex) {
            // modified....
            finalHtml += html.substring(lastIndex);
            html=finalHtml;
            finalHtml='';
            lastIndex=0;
        }
        /////////////////////////////////////////////////////////////
        //
        // Allow control of this modification
        // taKeepStyles: False - removes these modification
        //
        // taFixChrome removes the following styles:
        //    font-family: inherit;
        //    line-height: <number>
        //    color: inherit;
        //    color: rgb( <rgb-component>#{3} )
        //    background-color: rgb( <rgb-component>#{3} )
        //
        /////////////////////////////////////////////////////////////
        if (!keepStyles) {
            while (match = betterSpanMatch.exec(html)) {
                finalHtml += html.substring(lastIndex, match.index-1);
                styleVal = match[0];
                // test for chrome inserted junk
                match = /font-family: inherit;|line-height: 1.[0-9]{3,12};|color: inherit; line-height: 1.1;|color: rgb\(\d{1,3}, \d{1,3}, \d{1,3}\);|background-color: rgb\(\d{1,3}, \d{1,3}, \d{1,3}\);/gi.exec(styleVal);
                if (match) {
                    styleVal = styleVal.replace(/( |)font-family: inherit;|( |)line-height: 1.[0-9]{3,12};|( |)color: inherit;|( |)color: rgb\(\d{1,3}, \d{1,3}, \d{1,3}\);|( |)background-color: rgb\(\d{1,3}, \d{1,3}, \d{1,3}\);/ig, '');
                    //console.log(styleVal, styleVal.length);
                    if (styleVal.length > 8) {
                        finalHtml += ' ' + styleVal;
                    }
                } else {
                    finalHtml += ' ' + styleVal;
                }
                lastIndex = betterSpanMatch.lastIndex;
            }
            finalHtml += html.substring(lastIndex);
        }
        //console.log('final:', finalHtml);
        // only replace when something has changed, else we get focus problems on inserting lists
        if(lastIndex > 0){
            // replace all empty strings
            var fe = finalHtml.replace(/<span\s?>(.*?)<\/span>(<br(\/|)>|)/ig, '$1');
            return fe;
        } else return html;
    };
    return taFixChrome;
}).factory('taSanitize', ['$sanitize', function taSanitizeFactory($sanitize){

    var convert_infos = [
        {
            property: 'font-weight',
            values: [ 'bold' ],
            tag: 'b'
        },
        {
            property: 'font-style',
            values: [ 'italic' ],
            tag: 'i'
        }
    ];

    var styleMatch = [];
    for(var i = 0; i < convert_infos.length; i++){
        var _partialStyle = '(' + convert_infos[i].property + ':\\s*(';
        for(var j = 0; j < convert_infos[i].values.length; j++){
            /* istanbul ignore next: not needed to be tested yet */
            if(j > 0) _partialStyle += '|';
            _partialStyle += convert_infos[i].values[j];
        }
        _partialStyle += ');)';
        styleMatch.push(_partialStyle);
    }
    var styleRegexString = '(' + styleMatch.join('|') + ')';

    function wrapNested(html, wrapTag) {
        var depth = 0;
        var lastIndex = 0;
        var match;
        var tagRegex = /<[^>]*>/ig;
        while(match = tagRegex.exec(html)){
            lastIndex = match.index;
            if(match[0].substr(1, 1) === '/'){
                if(depth === 0) break;
                else depth--;
            }else depth++;
        }
        return wrapTag +
            html.substring(0, lastIndex) +
            // get the start tags reversed - this is safe as we construct the strings with no content except the tags
            angular.element(wrapTag)[0].outerHTML.substring(wrapTag.length) +
            html.substring(lastIndex);
    }

    function transformLegacyStyles(html){
        if(!html || !angular.isString(html) || html.length <= 0) return html;
        var i;
        var styleElementMatch = /<([^>\/]+?)style=("([^"]+)"|'([^']+)')([^>]*)>/ig;
        var match, subMatch, styleVal, newTag, lastNewTag = '', newHtml, finalHtml = '', lastIndex = 0;
        while(match = styleElementMatch.exec(html)){
            // one of the quoted values ' or "
            /* istanbul ignore next: quotations match */
            styleVal = match[3] || match[4];
            var styleRegex = new RegExp(styleRegexString, 'i');
            // test for style values to change
            if(angular.isString(styleVal) && styleRegex.test(styleVal)){
                // remove build tag list
                newTag = '';
                // init regex here for exec
                var styleRegexExec = new RegExp(styleRegexString, 'ig');
                // find relevand tags and build a string of them
                while(subMatch = styleRegexExec.exec(styleVal)){
                    for(i = 0; i < convert_infos.length; i++){
                        if(!!subMatch[(i*2) + 2]){
                            newTag += '<' + convert_infos[i].tag + '>';
                        }
                    }
                }
                // recursively find more legacy styles in html before this tag and after the previous match (if any)
                newHtml = transformLegacyStyles(html.substring(lastIndex, match.index));
                // build up html
                if(lastNewTag.length > 0){
                    finalHtml += wrapNested(newHtml, lastNewTag);
                }else finalHtml += newHtml;
                // grab the style val without the transformed values
                styleVal = styleVal.replace(new RegExp(styleRegexString, 'ig'), '');
                // build the html tag
                finalHtml += '<' + match[1].trim();
                if(styleVal.length > 0) finalHtml += ' style="' + styleVal + '"';
                finalHtml += match[5] + '>';
                // update the start index to after this tag
                lastIndex = match.index + match[0].length;
                lastNewTag = newTag;
            }
        }
        if(lastNewTag.length > 0){
            finalHtml += wrapNested(html.substring(lastIndex), lastNewTag);
        }
        else finalHtml += html.substring(lastIndex);
        return finalHtml;
    }

    function transformLegacyAttributes(html){
        if(!html || !angular.isString(html) || html.length <= 0) return html;
        // replace all align='...' tags with text-align attributes
        var attrElementMatch = /<([^>\/]+?)align=("([^"]+)"|'([^']+)')([^>]*)>/ig;
        var match, finalHtml = '', lastIndex = 0;
        // match all attr tags
        while(match = attrElementMatch.exec(html)){
            // add all html before this tag
            finalHtml += html.substring(lastIndex, match.index);
            // record last index after this tag
            lastIndex = match.index + match[0].length;
            // construct tag without the align attribute
            var newTag = '<' + match[1] + match[5];
            // add the style attribute
            if(/style=("([^"]+)"|'([^']+)')/ig.test(newTag)){
                /* istanbul ignore next: quotations match */
                newTag = newTag.replace(/style=("([^"]+)"|'([^']+)')/i, 'style="$2$3 text-align:' + (match[3] || match[4]) + ';"');
            }else{
                /* istanbul ignore next: quotations match */
                newTag += ' style="text-align:' + (match[3] || match[4]) + ';"';
            }
            newTag += '>';
            // add to html
            finalHtml += newTag;
        }
        // return with remaining html
        return finalHtml + html.substring(lastIndex);
    }

    // use precompiled regexp for speed
    var rsb1 = new RegExp(/<span id="selectionBoundary_\d+_\d+" class="rangySelectionBoundary">[^<>]+?<\/span>/ig);
    var rsb2 = new RegExp(/<span class="rangySelectionBoundary" id="selectionBoundary_\d+_\d+">[^<>]+?<\/span>/ig);
    var rsb3 = new RegExp(/<span id="selectionBoundary_\d+_\d+" class="rangySelectionBoundary">[^<>]+?<\/span>/ig);

    return function taSanitize(unsafe, oldsafe, ignore){
        // unsafe html should NEVER built into a DOM object via angular.element. This allows XSS to be inserted and run.
        if ( !ignore ) {
            try {
                unsafe = transformLegacyStyles(unsafe);
            } catch (e) {
            }
        }

        // unsafe and oldsafe should be valid HTML strings
        // any exceptions (lets say, color for example) should be made here but with great care
        // setup unsafe element for modification
        unsafe = transformLegacyAttributes(unsafe);

        // we had an issue in the past, where we dumped a whole bunch of <span>'s into the content...
        // so we remove them here
        // IN A FUTURE release this can be removed after all have updated through release 1.5.9
        if (unsafe) {
            try {
                unsafe = unsafe.replace(rsb1, '');
                unsafe = unsafe.replace(rsb2, '');
                unsafe = unsafe.replace(rsb1, '');
                unsafe = unsafe.replace(rsb3, '');
            } catch (e) {
            }
        }

        var safe;
        try {
            safe = $sanitize(unsafe);
            // do this afterwards, then the $sanitizer should still throw for bad markup
            if(ignore) safe = unsafe;
        } catch (e){
            safe = oldsafe || '';
        }

        // Do processing for <pre> tags, removing tabs and return carriages outside of them

        var _preTags = safe.match(/(<pre[^>]*>.*?<\/pre[^>]*>)/ig);
        var processedSafe = safe.replace(/(&#(9|10);)*/ig, '');
        var re = /<pre[^>]*>.*?<\/pre[^>]*>/ig;
        var index = 0;
        var lastIndex = 0;
        var origTag;
        safe = '';
        while((origTag = re.exec(processedSafe)) !== null && index < _preTags.length){
            safe += processedSafe.substring(lastIndex, origTag.index) + _preTags[index];
            lastIndex = origTag.index + origTag[0].length;
            index++;
        }
        return safe + processedSafe.substring(lastIndex);
    };
}]).factory('taToolExecuteAction', ['$q', '$log', function($q, $log){
    // this must be called on a toolScope or instance
    return function(editor){
        if(editor !== undefined) this.$editor = function(){ return editor; };
        var deferred = $q.defer(),
            promise = deferred.promise,
            _editor = this.$editor();
        // pass into the action the deferred function and also the function to reload the current selection if rangy available
        var result;
        try{
            result = this.action(deferred, _editor.startAction());
            // We set the .finally callback here to make sure it doesn't get executed before any other .then callback.
            promise['finally'](function(){
                _editor.endAction.call(_editor);
            });
        }catch(exc){
            $log.error(exc);
        }
        if(result || result === undefined){
            // if true or undefined is returned then the action has finished. Otherwise the deferred action will be resolved manually.
            deferred.resolve();
        }
    };
}]);

angular.module('textAngular.DOM', ['textAngular.factories'])
.factory('taExecCommand', ['taSelection', 'taBrowserTag', '$document', function(taSelection, taBrowserTag, $document){
    var listToDefault = function(listElement, defaultWrap){
        var $target, i;
        // if all selected then we should remove the list
        // grab all li elements and convert to taDefaultWrap tags
        var children = listElement.find('li');
        for(i = children.length - 1; i >= 0; i--){
            $target = angular.element('<' + defaultWrap + '>' + children[i].innerHTML + '</' + defaultWrap + '>');
            listElement.after($target);
        }
        listElement.remove();
        taSelection.setSelectionToElementEnd($target[0]);
    };
    var listElementToSelfTag = function(list, listElement, selfTag, bDefault, defaultWrap){
        var $target, i;
        // if all selected then we should remove the list
        // grab all li elements
        var priorElement;
        var nextElement;
        var children = list.find('li');
        var foundIndex;
        for (i = 0; i<children.length; i++) {
            if (children[i].outerHTML === listElement[0].outerHTML) {
                // found it...
                foundIndex = i;
                if (i>0) {
                    priorElement = children[i-1];
                }
                if (i+1<children.length) {
                    nextElement = children[i+1];
                }
                break;
            }
        }
        //console.log('listElementToSelfTag', list, listElement, selfTag, bDefault, priorElement, nextElement);
        // un-list the listElement
        var html = '';
        if (bDefault) {
            html += '<' + defaultWrap + '>' + listElement[0].innerHTML + '</' + defaultWrap + '>';
        } else {
            html += '<' + taBrowserTag(selfTag) + '>';
            html += '<li>' + listElement[0].innerHTML + '</li>';
            html += '</' + taBrowserTag(selfTag) + '>';
        }
        $target = angular.element(html);
        //console.log('$target', $target[0]);
        if (!priorElement) {
            // this is the first the list, so we just remove it...
            listElement.remove();
            list.after(angular.element(list[0].outerHTML));
            list.after($target);
            list.remove();
            taSelection.setSelectionToElementEnd($target[0]);
            return;
        } else if (!nextElement) {
            // this is the last in the list, so we just remove it..
            listElement.remove();
            list.after($target);
            taSelection.setSelectionToElementEnd($target[0]);
        } else {
            var p = list.parent();
            // okay it was some where in the middle... so we need to break apart the list...
            var html1 = '';
            var listTag = list[0].nodeName.toLowerCase();
            html1 += '<' + listTag + '>';
            for(i = 0; i < foundIndex; i++){
                html1 += '<li>' + children[i].innerHTML + '</li>';
            }
            html1 += '</' + listTag + '>';
            var html2 = '';
            html2 += '<' + listTag + '>';
            for(i = foundIndex+1; i < children.length; i++){
                html2 += '<li>' + children[i].innerHTML + '</li>';
            }
            html2 += '</' + listTag + '>';
            //console.log(html1, $target[0], html2);
            list.after(angular.element(html2));
            list.after($target);
            list.after(angular.element(html1));
            list.remove();
            //console.log('parent ******XXX*****', p[0]);
            taSelection.setSelectionToElementEnd($target[0]);
        }
    };
    var listElementsToSelfTag = function(list, listElements, selfTag, bDefault, defaultWrap){
        var $target, i, j, p;
        // grab all li elements
        var priorElement;
        var afterElement;
        //console.log('list:', list, 'listElements:', listElements, 'selfTag:', selfTag, 'bDefault:', bDefault);
        var children = list.find('li');
        var foundIndexes = [];
        for (i = 0; i<children.length; i++) {
            for (j = 0; j<listElements.length; j++) {
                if (children[i].isEqualNode(listElements[j])) {
                    // found it...
                    foundIndexes[j] = i;
                }
            }
        }
        if (foundIndexes[0] > 0) {
            priorElement = children[foundIndexes[0] - 1];
        }
        if (foundIndexes[listElements.length-1] + 1 < children.length) {
            afterElement = children[foundIndexes[listElements.length-1] + 1];
        }
        //console.log('listElementsToSelfTag', list, listElements, selfTag, bDefault, !priorElement, !afterElement, foundIndexes[listElements.length-1], children.length);
        // un-list the listElements
        var html = '';
        if (bDefault) {
            for (j = 0; j < listElements.length; j++) {
                html += '<' + defaultWrap + '>' + listElements[j].innerHTML + '</' + defaultWrap + '>';
                listElements[j].remove();
            }
        } else {
            html += '<' + taBrowserTag(selfTag) + '>';
            for (j = 0; j < listElements.length; j++) {
                html += listElements[j].outerHTML;
                listElements[j].remove();
            }
            html += '</' + taBrowserTag(selfTag) + '>';
        }
        $target = angular.element(html);
        if (!priorElement) {
            // this is the first the list, so we just remove it...
            list.after(angular.element(list[0].outerHTML));
            list.after($target);
            list.remove();
            taSelection.setSelectionToElementEnd($target[0]);
            return;
        } else if (!afterElement) {
            // this is the last in the list, so we just remove it..
            list.after($target);
            taSelection.setSelectionToElementEnd($target[0]);
            return;
        } else {
            // okay it was some where in the middle... so we need to break apart the list...
            var html1 = '';
            var listTag = list[0].nodeName.toLowerCase();
            html1 += '<' + listTag + '>';
            for(i = 0; i < foundIndexes[0]; i++){
                html1 += '<li>' + children[i].innerHTML + '</li>';
            }
            html1 += '</' + listTag + '>';
            var html2 = '';
            html2 += '<' + listTag + '>';
            for(i = foundIndexes[listElements.length-1]+1; i < children.length; i++){
                html2 += '<li>' + children[i].innerHTML + '</li>';
            }
            html2 += '</' + listTag + '>';
            list.after(angular.element(html2));
            list.after($target);
            list.after(angular.element(html1));
            list.remove();
            //console.log('parent ******YYY*****', list.parent()[0]);
            taSelection.setSelectionToElementEnd($target[0]);
        }
    };
    var selectLi = function(liElement){
        if(/(<br(|\/)>)$/i.test(liElement.innerHTML.trim())) taSelection.setSelectionBeforeElement(angular.element(liElement).find("br")[0]);
        else taSelection.setSelectionToElementEnd(liElement);
    };
    var listToList = function(listElement, newListTag){
        var $target = angular.element('<' + newListTag + '>' + listElement[0].innerHTML + '</' + newListTag + '>');
        listElement.after($target);
        listElement.remove();
        selectLi($target.find('li')[0]);
    };
    var childElementsToList = function(elements, listElement, newListTag){
        var html = '';
        for(var i = 0; i < elements.length; i++){
            html += '<' + taBrowserTag('li') + '>' + elements[i].innerHTML + '</' + taBrowserTag('li') + '>';
        }
        var $target = angular.element('<' + newListTag + '>' + html + '</' + newListTag + '>');
        listElement.after($target);
        listElement.remove();
        selectLi($target.find('li')[0]);
    };
    var turnBlockIntoBlocks = function(element, options) {
        for(var i = 0; i<element.childNodes.length; i++) {
            var _n = element.childNodes[i];
            /* istanbul ignore next - more complex testing*/
            if (_n.tagName && _n.tagName.match(BLOCKELEMENTS)) {
                turnBlockIntoBlocks(_n, options);
            }
        }
        /* istanbul ignore next - very rare condition that we do not test*/
        if (element.parentNode === null) {
            // nothing left to do..
            return element;
        }
        /* istanbul ignore next - not sure have to test this */
        if (options === '<br>'){
            return element;
        }
        else {
            var $target = angular.element(options);
            $target[0].innerHTML = element.innerHTML;
            element.parentNode.insertBefore($target[0], element);
            element.parentNode.removeChild(element);
            return $target;
        }
    };
    return function(taDefaultWrap, topNode){
        // NOTE: here we are dealing with the html directly from the browser and not the html the user sees.
        // IF you want to modify the html the user sees, do it when the user does a switchView
        taDefaultWrap = taBrowserTag(taDefaultWrap);
        return function(command, showUI, options, defaultTagAttributes){
            var i, $target, html, _nodes, next, optionsTagName, selectedElement, ourSelection;
            var defaultWrapper = angular.element('<' + taDefaultWrap + '>');
            try{
                if (taSelection.getSelection) {
                    ourSelection = taSelection.getSelection();
                }
                selectedElement = taSelection.getSelectionElement();
                // special checks and fixes when we are selecting the whole container
                var __h, _innerNode;
                /* istanbul ignore next */
                if (selectedElement.tagName !== undefined) {
                    if (selectedElement.tagName.toLowerCase() === 'div' &&
                        /taTextElement.+/.test(selectedElement.id) &&
                        ourSelection && ourSelection.start &&
                        ourSelection.start.offset === 1 &&
                        ourSelection.end.offset === 1) {
                        // opps we are actually selecting the whole container!
                        //console.log('selecting whole container!');
                        __h = selectedElement.innerHTML;
                        if (/<br>/i.test(__h)) {
                            // Firefox adds <br>'s and so we remove the <br>
                            __h = __h.replace(/<br>/i, '&#8203;');  // no space-space
                        }
                        if (/<br\/>/i.test(__h)) {
                            // Firefox adds <br/>'s and so we remove the <br/>
                            __h = __h.replace(/<br\/>/i, '&#8203;');  // no space-space
                        }
                        // remove stacked up <span>'s
                        if (/<span>(<span>)+/i.test(__h)) {
                            __h = __.replace(/<span>(<span>)+/i, '<span>');
                        }
                        // remove stacked up </span>'s
                        if (/<\/span>(<\/span>)+/i.test(__h)) {
                            __h = __.replace(/<\/span>(<\/span>)+/i, '<\/span>');
                        }
                        if (/<span><\/span>/i.test(__h)) {
                            // if we end up with a <span></span> here we remove it...
                            __h = __h.replace(/<span><\/span>/i, '');
                        }
                        //console.log('inner whole container', selectedElement.childNodes);
                        _innerNode = '<div>' + __h + '</div>';
                        selectedElement.innerHTML = _innerNode;
                        taSelection.setSelectionToElementEnd(selectedElement.childNodes[0]);
                        selectedElement = taSelection.getSelectionElement();
                    } else if (selectedElement.tagName.toLowerCase() === 'span' &&
                        ourSelection && ourSelection.start &&
                        ourSelection.start.offset === 1 &&
                        ourSelection.end.offset === 1) {
                        // just a span -- this is a problem...
                        //console.log('selecting span!');
                        __h = selectedElement.innerHTML;
                        if (/<br>/i.test(__h)) {
                            // Firefox adds <br>'s and so we remove the <br>
                            __h = __h.replace(/<br>/i, '&#8203;');  // no space-space
                        }
                        if (/<br\/>/i.test(__h)) {
                            // Firefox adds <br/>'s and so we remove the <br/>
                            __h = __h.replace(/<br\/>/i, '&#8203;');  // no space-space
                        }
                        // remove stacked up <span>'s
                        if (/<span>(<span>)+/i.test(__h)) {
                            __h = __.replace(/<span>(<span>)+/i, '<span>');
                        }
                        // remove stacked up </span>'s
                        if (/<\/span>(<\/span>)+/i.test(__h)) {
                            __h = __.replace(/<\/span>(<\/span>)+/i, '<\/span>');
                        }
                        if (/<span><\/span>/i.test(__h)) {
                            // if we end up with a <span></span> here we remove it...
                            __h = __h.replace(/<span><\/span>/i, '');
                        }
                        //console.log('inner span', selectedElement.childNodes);
                        // we wrap this in a <div> because otherwise the browser get confused when we attempt to select the whole node
                        // and the focus is not set correctly no matter what we do
                        _innerNode = '<div>' + __h + '</div>';
                        selectedElement.innerHTML = _innerNode;
                        taSelection.setSelectionToElementEnd(selectedElement.childNodes[0]);
                        selectedElement = taSelection.getSelectionElement();
                        //console.log(selectedElement.innerHTML);
                    } else if (selectedElement.tagName.toLowerCase() === 'p' &&
                        ourSelection && ourSelection.start &&
                        ourSelection.start.offset === 1 &&
                        ourSelection.end.offset === 1) {
                        //console.log('p special');
                        // we need to remove the </br> that firefox adds!
                        __h = selectedElement.innerHTML;
                        if (/<br>/i.test(__h)) {
                            // Firefox adds <br>'s and so we remove the <br>
                            __h = __h.replace(/<br>/i, '&#8203;');  // no space-space
                            selectedElement.innerHTML = __h;
                        }
                    } else if (selectedElement.tagName.toLowerCase() === 'li' &&
                        ourSelection && ourSelection.start &&
                        ourSelection.start.offset === ourSelection.end.offset) {
                        // we need to remove the </br> that firefox adds!
                        __h = selectedElement.innerHTML;
                        if (/<br>/i.test(__h)) {
                            // Firefox adds <br>'s and so we remove the <br>
                            __h = __h.replace(/<br>/i, '');  // nothing
                            selectedElement.innerHTML = __h;
                        }
                    }
                }
            }catch(e){}
            //console.log('************** selectedElement:', selectedElement);
            /* istanbul ignore if: */
            if (!selectedElement){return;}
            var $selected = angular.element(selectedElement);
            var tagName = (selectedElement && selectedElement.tagName && selectedElement.tagName.toLowerCase()) ||
                /* istanbul ignore next: */ "";
            if(command.toLowerCase() === 'insertorderedlist' || command.toLowerCase() === 'insertunorderedlist'){
                var selfTag = taBrowserTag((command.toLowerCase() === 'insertorderedlist')? 'ol' : 'ul');
                var selectedElements = taSelection.getOnlySelectedElements();
                //console.log('PPPPPPPPPPPPP', tagName, selfTag, selectedElements, tagName.match(BLOCKELEMENTS), $selected.hasClass('ta-bind'), $selected.parent()[0].tagName);
                if (selectedElements.length>1 && (tagName === 'ol' ||  tagName === 'ul' )) {
                    return listElementsToSelfTag($selected, selectedElements, selfTag, selfTag===tagName, taDefaultWrap);
                }
                if(tagName === selfTag){
                    // if all selected then we should remove the list
                    // grab all li elements and convert to taDefaultWrap tags
                    //console.log('tagName===selfTag');
                    if ($selected[0].childNodes.length !== selectedElements.length && selectedElements.length===1) {
                        $selected = angular.element(selectedElements[0]);
                        return listElementToSelfTag($selected.parent(), $selected, selfTag, true, taDefaultWrap);
                    } else {
                        return listToDefault($selected, taDefaultWrap);
                    }
                }else if(tagName === 'li' &&
                    $selected.parent()[0].tagName.toLowerCase() === selfTag &&
                    $selected.parent().children().length === 1){
                    // catch for the previous statement if only one li exists
                    return listToDefault($selected.parent(), taDefaultWrap);
                }else if(tagName === 'li' &&
                    $selected.parent()[0].tagName.toLowerCase() !== selfTag &&
                    $selected.parent().children().length === 1){
                    // catch for the previous statement if only one li exists
                    return listToList($selected.parent(), selfTag);
                }else if(tagName.match(BLOCKELEMENTS) && !$selected.hasClass('ta-bind')){
                    // if it's one of those block elements we have to change the contents
                    // if it's a ol/ul we are changing from one to the other
                    if (selectedElements.length) {
                        if ($selected[0].childNodes.length !== selectedElements.length && selectedElements.length===1) {
                            //console.log('&&&&&&&&&&&&&&& --------- &&&&&&&&&&&&&&&&', selectedElements[0], $selected[0].childNodes);
                            $selected = angular.element(selectedElements[0]);
                            return listElementToSelfTag($selected.parent(), $selected, selfTag, selfTag===tagName, taDefaultWrap);
                        }
                    }
                    if(tagName === 'ol' || tagName === 'ul'){
                        // now if this is a set of selected elements... behave diferently
                        return listToList($selected, selfTag);
                    }else{
                        var childBlockElements = false;
                        angular.forEach($selected.children(), function(elem){
                            if(elem.tagName.match(BLOCKELEMENTS)) {
                                childBlockElements = true;
                            }
                        });
                        if(childBlockElements){
                            return childElementsToList($selected.children(), $selected, selfTag);
                        }else{
                            return childElementsToList([angular.element('<div>' + selectedElement.innerHTML + '</div>')[0]], $selected, selfTag);
                        }
                    }
                }else if(tagName.match(BLOCKELEMENTS)){
                    // if we get here then the contents of the ta-bind are selected
                    _nodes = taSelection.getOnlySelectedElements();
                    //console.log('_nodes', _nodes, tagName);
                    if(_nodes.length === 0){
                        // here is if there is only text in ta-bind ie <div ta-bind>test content</div>
                        $target = angular.element('<' + selfTag + '><li>' + selectedElement.innerHTML + '</li></' + selfTag + '>');
                        $selected.html('');
                        $selected.append($target);
                    }else if(_nodes.length === 1 && (_nodes[0].tagName.toLowerCase() === 'ol' || _nodes[0].tagName.toLowerCase() === 'ul')){
                        if(_nodes[0].tagName.toLowerCase() === selfTag){
                            // remove
                            return listToDefault(angular.element(_nodes[0]), taDefaultWrap);
                        }else{
                            return listToList(angular.element(_nodes[0]), selfTag);
                        }
                    }else{
                        html = '';
                        var $nodes = [];
                        for(i = 0; i < _nodes.length; i++){
                            /* istanbul ignore else: catch for real-world can't make it occur in testing */
                            if(_nodes[i].nodeType !== 3){
                                var $n = angular.element(_nodes[i]);
                                /* istanbul ignore if: browser check only, phantomjs doesn't return children nodes but chrome at least does */
                                if(_nodes[i].tagName.toLowerCase() === 'li') continue;
                                else if(_nodes[i].tagName.toLowerCase() === 'ol' || _nodes[i].tagName.toLowerCase() === 'ul'){
                                    html += $n[0].innerHTML; // if it's a list, add all it's children
                                }else if(_nodes[i].tagName.toLowerCase() === 'span' && (_nodes[i].childNodes[0].tagName.toLowerCase() === 'ol' || _nodes[i].childNodes[0].tagName.toLowerCase() === 'ul')){
                                    html += $n[0].childNodes[0].innerHTML; // if it's a list, add all it's children
                                }else{
                                    html += '<' + taBrowserTag('li') + '>' + $n[0].innerHTML + '</' + taBrowserTag('li') + '>';
                                }
                                $nodes.unshift($n);
                            }
                        }
                        //console.log('$nodes', $nodes);
                        $target = angular.element('<' + selfTag + '>' + html + '</' + selfTag + '>');
                        $nodes.pop().replaceWith($target);
                        angular.forEach($nodes, function($node){ $node.remove(); });
                    }
                    taSelection.setSelectionToElementEnd($target[0]);
                    return;
                }
            }else if(command.toLowerCase() === 'formatblock'){
                optionsTagName = options.toLowerCase().replace(/[<>]/ig, '');
                if(optionsTagName.trim() === 'default') {
                    optionsTagName = taDefaultWrap;
                    options = '<' + taDefaultWrap + '>';
                }
                if(tagName === 'li') {
                    $target = $selected.parent();
                }
                else {
                    $target = $selected;
                }
                // find the first blockElement
                while(!$target[0].tagName || !$target[0].tagName.match(BLOCKELEMENTS) && !$target.parent().attr('contenteditable')){
                    $target = $target.parent();
                    /* istanbul ignore next */
                    tagName = ($target[0].tagName || '').toLowerCase();
                }
                if(tagName === optionsTagName){
                    // $target is wrap element
                    _nodes = $target.children();
                    var hasBlock = false;
                    for(i = 0; i < _nodes.length; i++){
                        hasBlock = hasBlock || _nodes[i].tagName.match(BLOCKELEMENTS);
                    }
                    if(hasBlock){
                        $target.after(_nodes);
                        next = $target.next();
                        $target.remove();
                        $target = next;
                    }else{
                        defaultWrapper.append($target[0].childNodes);
                        $target.after(defaultWrapper);
                        $target.remove();
                        $target = defaultWrapper;
                    }
                }else if($target.parent()[0].tagName.toLowerCase() === optionsTagName &&
                    !$target.parent().hasClass('ta-bind')){
                    //unwrap logic for parent
                    var blockElement = $target.parent();
                    var contents = blockElement.contents();
                    for(i = 0; i < contents.length; i ++){
                        /* istanbul ignore next: can't test - some wierd thing with how phantomjs works */
                        if(blockElement.parent().hasClass('ta-bind') && contents[i].nodeType === 3){
                            defaultWrapper = angular.element('<' + taDefaultWrap + '>');
                            defaultWrapper[0].innerHTML = contents[i].outerHTML;
                            contents[i] = defaultWrapper[0];
                        }
                        blockElement.parent()[0].insertBefore(contents[i], blockElement[0]);
                    }
                    blockElement.remove();
                }else if(tagName.match(LISTELEMENTS)){
                    // wrapping a list element
                    $target.wrap(options);
                }else{
                    // default wrap behaviour
                    _nodes = taSelection.getOnlySelectedElements();
                    if(_nodes.length === 0) {
                        // no nodes at all....
                        _nodes = [$target[0]];
                    }
                    // find the parent block element if any of the nodes are inline or text
                    for(i = 0; i < _nodes.length; i++){
                        if(_nodes[i].nodeType === 3 || !_nodes[i].tagName.match(BLOCKELEMENTS)){
                            while(_nodes[i].nodeType === 3 || !_nodes[i].tagName || !_nodes[i].tagName.match(BLOCKELEMENTS)){
                                _nodes[i] = _nodes[i].parentNode;
                            }
                        }
                    }
                    // remove any duplicates from the array of _nodes!
                    _nodes = _nodes.filter(function(value, index, self) {
                        return self.indexOf(value) === index;
                    });
                    // remove all whole taTextElement if it is here... unless it is the only element!
                    if (_nodes.length>1) {
                        _nodes = _nodes.filter(function (value, index, self) {
                            return !(value.nodeName.toLowerCase() === 'div' && /^taTextElement/.test(value.id));
                        });
                    }
                    if(angular.element(_nodes[0]).hasClass('ta-bind')){
                        $target = angular.element(options);
                        $target[0].innerHTML = _nodes[0].innerHTML;
                        _nodes[0].innerHTML = $target[0].outerHTML;
                    }else if(optionsTagName === 'blockquote'){
                        // blockquotes wrap other block elements
                        html = '';
                        for(i = 0; i < _nodes.length; i++){
                            html += _nodes[i].outerHTML;
                        }
                        $target = angular.element(options);
                        $target[0].innerHTML = html;
                        _nodes[0].parentNode.insertBefore($target[0],_nodes[0]);
                        for(i = _nodes.length - 1; i >= 0; i--){
                            /* istanbul ignore else:  */
                            if (_nodes[i].parentNode) _nodes[i].parentNode.removeChild(_nodes[i]);
                        }
                    } else /* istanbul ignore next: not tested since identical to blockquote */
                    if (optionsTagName === 'pre' && taSelection.getStateShiftKey()) {
                        //console.log('shift pre', _nodes);
                        // pre wrap other block elements
                        html = '';
                        for (i = 0; i < _nodes.length; i++) {
                            html += _nodes[i].outerHTML;
                        }
                        $target = angular.element(options);
                        $target[0].innerHTML = html;
                        _nodes[0].parentNode.insertBefore($target[0], _nodes[0]);
                        for (i = _nodes.length - 1; i >= 0; i--) {
                            /* istanbul ignore else:  */
                            if (_nodes[i].parentNode) _nodes[i].parentNode.removeChild(_nodes[i]);
                        }
                    }
                    else {
                        //console.log(optionsTagName, _nodes);
                        // regular block elements replace other block elements
                        for (i = 0; i < _nodes.length; i++) {
                            var newBlock = turnBlockIntoBlocks(_nodes[i], options);
                            if (_nodes[i] === $target[0]) {
                                $target = angular.element(newBlock);
                            }
                        }
                    }
                }
                taSelection.setSelectionToElementEnd($target[0]);
                // looses focus when we have the whole container selected and no text!
                // refocus on the shown display element, this fixes a bug when using firefox
                $target[0].focus();
                return;
            }else if(command.toLowerCase() === 'createlink'){
                /* istanbul ignore next: firefox specific fix */
                if (tagName === 'a') {
                    // already a link!!! we are just replacing it...
                    taSelection.getSelectionElement().href = options;
                    return;
                }
                var tagBegin = '<a href="' + options + '" target="' +
                        (defaultTagAttributes.a.target ? defaultTagAttributes.a.target : '') +
                        '">',
                    tagEnd = '</a>',
                    _selection = taSelection.getSelection();
                if(_selection.collapsed){
                    //console.log('collapsed');
                    // insert text at selection, then select then just let normal exec-command run
                    taSelection.insertHtml(tagBegin + options + tagEnd, topNode);
                }else if(rangy.getSelection().getRangeAt(0).canSurroundContents()){
                    var node = angular.element(tagBegin + tagEnd)[0];
                    rangy.getSelection().getRangeAt(0).surroundContents(node);
                }
                return;
            }else if(command.toLowerCase() === 'inserthtml'){
                //console.log('inserthtml');
                taSelection.insertHtml(options, topNode);
                return;
            }
            try{
                $document[0].execCommand(command, showUI, options);
            }catch(e){}
        };
    };
}]).service('taSelection', ['$document', 'taDOM', '$log',
/* istanbul ignore next: all browser specifics and PhantomJS dosen't seem to support half of it */
function($document, taDOM, $log){
    // need to dereference the document else the calls don't work correctly
    var _document = $document[0];
    var bShiftState;
    var brException = function (element, offset) {
        /* check if selection is a BR element at the beginning of a container. If so, get
        * the parentNode instead.
        * offset should be zero in this case. Otherwise, return the original
        * element.
        */
        if (element.tagName && element.tagName.match(/^br$/i) && offset === 0 && !element.previousSibling) {
            return {
                element: element.parentNode,
                offset: 0
            };
        } else {
            return {
                element: element,
                offset: offset
            };
        }
    };
    var api = {
        getSelection: function(){
            var range;
            try {
                // catch any errors from rangy and ignore the issue
                range = rangy.getSelection().getRangeAt(0);
            } catch(e) {
                //console.info(e);
                return undefined;
            }
            var container = range.commonAncestorContainer;
            var selection = {
                start: brException(range.startContainer, range.startOffset),
                end: brException(range.endContainer, range.endOffset),
                collapsed: range.collapsed
            };
            // This has problems under Firefox.
            // On Firefox with
            // <p>Try me !</p>
            // <ul>
            // <li>line 1</li>
            // <li>line 2</li>
            // </ul>
            // <p>line 3</p>
            // <ul>
            // <li>line 4</li>
            // <li>line 5</li>
            // </ul>
            // <p>Hello textAngular</p>
            // WITH the cursor after the 3 on line 3, it gets the commonAncestorContainer as:
            // <TextNode textContent='line 3'>
            // AND Chrome gets the commonAncestorContainer as:
            // <p>line 3</p>
            //
            // Check if the container is a text node and return its parent if so
            // unless this is the whole taTextElement.  If so we return the textNode
            if (container.nodeType === 3) {
                if (container.parentNode.nodeName.toLowerCase() === 'div' &&
                    /^taTextElement/.test(container.parentNode.id)) {
                    // textNode where the parent is the whole <div>!!!
                    //console.log('textNode ***************** container:', container);
                } else {
                    container = container.parentNode;
                }
            }
            if (container.nodeName.toLowerCase() === 'div' &&
                /^taTextElement/.test(container.id)) {
                //console.log('*********taTextElement************');
                //console.log('commonAncestorContainer:', container);
                selection.start.element = container.childNodes[selection.start.offset];
                selection.end.element = container.childNodes[selection.end.offset];
                selection.container = container;
            } else {
                if (container.parentNode === selection.start.element ||
                    container.parentNode === selection.end.element) {
                    selection.container = container.parentNode;
                } else {
                    selection.container = container;
                }
            }
            //console.log('***selection container:', selection.container.nodeName, selection.start.offset, selection.container);
            return selection;
        },
        // if we use the LEFT_ARROW and we are at the special place <span>&#65279;</span> we move the cursor over by one...
        // Chrome and Firefox behave differently so so fix this for Firefox here.  No adjustment needed for Chrome.
        updateLeftArrowKey: function(element) {
            var range = rangy.getSelection().getRangeAt(0);
            if (range && range.collapsed) {
                var _nodes = api.getFlattenedDom(range);
                if (!_nodes.findIndex) return;
                var _node = range.startContainer;
                var indexStartContainer = _nodes.findIndex(function(element, index){
                    if (element.node===_node) return true;
                    var _indexp = element.parents.indexOf(_node);
                    return (_indexp !== -1);
                });
                var m;
                var nextNodeToRight;
                //console.log('indexStartContainer', indexStartContainer, _nodes.length, 'startContainer:', _node, _node === _nodes[indexStartContainer].node);
                _nodes.forEach(function (n, i) {
                    //console.log(i, n.node);
                    n.parents.forEach(function (nn, j){
                        //console.log(i, j, nn);
                    });
                });
                if (indexStartContainer+1 < _nodes.length) {
                    // we need the node just after this startContainer
                    // so we can check and see it this is a special place
                    nextNodeToRight = _nodes[indexStartContainer+1].node;
                    //console.log(nextNodeToRight, range.startContainer);
                }
                //console.log('updateLeftArrowKey', range.startOffset, range.startContainer.textContent);
                // this first section handles the case for Chrome browser
                // if the first character of the nextNode is a \ufeff we know that we are just before the special span...
                // and so we most left by one character
                if (nextNodeToRight && nextNodeToRight.textContent) {
                    m = /^\ufeff([^\ufeff]*)$/.exec(nextNodeToRight.textContent);
                    if (m) {
                        // we are before the special node with begins with a \ufeff character
                        //console.log('LEFT ...found it...', 'startOffset:', range.startOffset, m[0].length, m[1].length);
                        // no need to change anything in this case
                        return;
                    }
                }
                var nextNodeToLeft;
                if (indexStartContainer > 0) {
                    // we need the node just after this startContainer
                    // so we can check and see it this is a special place
                    nextNodeToLeft = _nodes[indexStartContainer-1].node;
                    //console.log(nextNodeToLeft, nextNodeToLeft);
                }
                if (range.startOffset === 0 && nextNodeToLeft) {
                    //console.log(nextNodeToLeft, range.startOffset, nextNodeToLeft.textContent);
                    m = /^\ufeff([^\ufeff]*)$/.exec(nextNodeToLeft.textContent);
                    if (m) {
                        //console.log('LEFT &&&&&&&&&&&&&&&&&&&...found it...&&&&&&&&&&&', nextNodeToLeft, m[0].length, m[1].length);
                        // move over to the left my one -- Firefox triggers this case
                        api.setSelectionToElementEnd(nextNodeToLeft);
                        return;
                    }
                }
            }
            return;
        },
        // if we use the RIGHT_ARROW and we are at the special place <span>&#65279;</span> we move the cursor over by one...
        updateRightArrowKey: function(element) {
            // we do not need to make any adjustments here, so we ignore all this code
            if (false) {
                var range = rangy.getSelection().getRangeAt(0);
                if (range && range.collapsed) {
                    var _nodes = api.getFlattenedDom(range);
                    if (!_nodes.findIndex) return;
                    var _node = range.startContainer;
                    var indexStartContainer = _nodes.findIndex(function (element, index) {
                        if (element.node === _node) return true;
                        var _indexp = element.parents.indexOf(_node);
                        return (_indexp !== -1);
                    });
                    var _sel;
                    var i;
                    var m;

                    // if the last character is a \ufeff we know that we are just before the special span...
                    // and so we most right by one character
                    var indexFound = _nodes.findIndex(function (n, index) {
                        if (n.textContent) {
                            var m = /^\ufeff([^\ufeff]*)$/.exec(n.textContent);
                            if (m) {
                                return true;
                            } else {
                                return false;
                            }
                        } else {
                            return false;
                        }
                    });
                    if (indexFound === -1) {
                        return;
                    }
                    //console.log(indexFound, range.startContainer, range.startOffset);
                    _node = _nodes[indexStartContainer];
                    //console.log('indexStartContainer', indexStartContainer);
                    if (_node && _node.textContent) {
                        m = /^\ufeff([^\ufeff]*)$/.exec(_node.textContent);
                        if (m && range.startOffset - 1 === m[1].length) {
                            //console.log('RIGHT found it...&&&&&&&&&&&', range.startOffset);
                            // no need to make any adjustment
                            return;
                        }
                    }
                    //console.log(range.startOffset);
                    if (_nodes && range.startOffset === 0) {
                        indexStartContainer = _nodes.indexOf(range.startContainer);
                        if (indexStartContainer !== -1 && indexStartContainer > 0) {
                            _node = _nodes[indexStartContainer - 1];
                            if (_node.textContent) {
                                m = /\ufeff([^\ufeff]*)$/.exec(_node.textContent);
                                if (m && true || range.startOffset === m[1].length + 1) {
                                    //console.log('RIGHT &&&&&&&&&&&&&&&&&&&...found it...&&&&&&&&&&&', range.startOffset, m[1].length);
                                    // no need to make any adjustment
                                    return;
                                }
                            }
                        }
                    }
                }
            }
        },
        getFlattenedDom: function(range) {
            var parent = range.commonAncestorContainer.parentNode;
            if (!parent) {
                return range.commonAncestorContainer.childNodes;
            }
            var nodes = Array.prototype.slice.call(parent.childNodes); // converts NodeList to Array
            var indexStartContainer = nodes.indexOf(range.startContainer);
            // make sure that we have a big enough set of nodes
            if (indexStartContainer+1 < nodes.length && indexStartContainer > 0) {
                // we are good
                // we can go down one node or up one node
            } else {
                if (parent.parentNode) {
                    parent = parent.parentNode;
                }
            }
            // now walk the parent
            nodes = [];
            function addNodes(_set) {
                if (_set.node.childNodes.length) {
                    var childNodes = Array.prototype.slice.call(_set.node.childNodes); // converts NodeList to Array
                    childNodes.forEach(function(n) {
                        var _t = _set.parents.slice();
                        if (_t.slice(-1)[0]!==_set.node) {
                            _t.push(_set.node);
                        }
                        addNodes({parents: _t, node: n});
                    });
                } else {
                    nodes.push({parents: _set.parents, node: _set.node});
                }
            }
            addNodes({parents: [parent], node: parent});
            return nodes;
        },
        getOnlySelectedElements: function(){
            var range = rangy.getSelection().getRangeAt(0);
            var container = range.commonAncestorContainer;
            // Node.TEXT_NODE === 3
            // Node.ELEMENT_NODE === 1
            // Node.COMMENT_NODE === 8
            // Check if the container is a text node and return its parent if so
            container = container.nodeType === 3 ? container.parentNode : container;
            // get the nodes in the range that are ELEMENT_NODE and are children of the container
            // in this range...
            return range.getNodes([1], function(node){
                return node.parentNode === container;
            });
        },
        // this includes the container element if all children are selected
        getAllSelectedElements: function(){
            var range = rangy.getSelection().getRangeAt(0);
            var container = range.commonAncestorContainer;
            // Node.TEXT_NODE === 3
            // Node.ELEMENT_NODE === 1
            // Node.COMMENT_NODE === 8
            // Check if the container is a text node and return its parent if so
            container = container.nodeType === 3 ? container.parentNode : container;
            // get the nodes in the range that are ELEMENT_NODE and are children of the container
            // in this range...
            var selectedNodes = range.getNodes([1], function(node){
                return node.parentNode === container;
            });
            var innerHtml = container.innerHTML;
            // remove the junk that rangy has put down
            innerHtml = innerHtml.replace(/<span id=.selectionBoundary[^>]+>\ufeff?<\/span>/ig, '');
            //console.log(innerHtml);
            //console.log(range.toHtml());
            //console.log(innerHtml === range.toHtml());
            if (innerHtml === range.toHtml() &&
                // not the whole taTextElement
                (!(container.nodeName.toLowerCase() === 'div' &&  /^taTextElement/.test(container.id)))
            ) {
                var arr = [];
                for(var i = selectedNodes.length; i--; arr.unshift(selectedNodes[i]));
                selectedNodes = arr;
                selectedNodes.push(container);
                //$log.debug(selectedNodes);
            }
            return selectedNodes;
        },
        // Some basic selection functions
        getSelectionElement: function () {
            var s = api.getSelection();
            if (s) {
                return api.getSelection().container;
            } else {
                return undefined;
            }
        },
        setSelection: function(elStart, elEnd, start, end){
            var range = rangy.createRange();

            range.setStart(elStart, start);
            range.setEnd(elEnd, end);

            rangy.getSelection().setSingleRange(range);
        },
        setSelectionBeforeElement: function (el){
            var range = rangy.createRange();

            range.selectNode(el);
            range.collapse(true);

            rangy.getSelection().setSingleRange(range);
        },
        setSelectionAfterElement: function (el){
            var range = rangy.createRange();

            range.selectNode(el);
            range.collapse(false);

            rangy.getSelection().setSingleRange(range);
        },
        setSelectionToElementStart: function (el){
            var range = rangy.createRange();

            range.selectNodeContents(el);
            range.collapse(true);

            rangy.getSelection().setSingleRange(range);
        },
        setSelectionToElementEnd: function (el){
            var range = rangy.createRange();

            range.selectNodeContents(el);
            range.collapse(false);
            if(el.childNodes && el.childNodes[el.childNodes.length - 1] && el.childNodes[el.childNodes.length - 1].nodeName === 'br'){
                range.startOffset = range.endOffset = range.startOffset - 1;
            }
            rangy.getSelection().setSingleRange(range);
        },
        setStateShiftKey: function(bS) {
            bShiftState = bS;
        },
        getStateShiftKey: function() {
            return bShiftState;
        },
        // from http://stackoverflow.com/questions/6690752/insert-html-at-caret-in-a-contenteditable-div
        // topNode is the contenteditable normally, all manipulation MUST be inside this.
        insertHtml: function(html, topNode){
            var parent, secondParent, _childI, nodes, i, lastNode, _tempFrag;
            var element = angular.element("<div>" + html + "</div>");
            var range = rangy.getSelection().getRangeAt(0);
            var frag = _document.createDocumentFragment();
            var children = element[0].childNodes;
            var isInline = true;

            if(children.length > 0){
                // NOTE!! We need to do the following:
                // check for blockelements - if they exist then we have to split the current element in half (and all others up to the closest block element) and insert all children in-between.
                // If there are no block elements, or there is a mixture we need to create textNodes for the non wrapped text (we don't want them spans messing up the picture).
                nodes = [];
                for(_childI = 0; _childI < children.length; _childI++){
                    var _cnode = children[_childI];
                    if (_cnode.nodeName.toLowerCase() === 'p' &&
                        _cnode.innerHTML.trim() === '') { // empty p element
                        continue;
                    }
                    /****************
                     *  allow any text to be inserted...
                    if((   _cnode.nodeType === 3 &&
                           _cnode.nodeValue === '\ufeff'[0] &&
                           _cnode.nodeValue.trim() === '') // empty no-space space element
                        ) {
                        // no change to isInline
                        nodes.push(_cnode);
                        continue;
                    }
                    if(_cnode.nodeType === 3 &&
                         _cnode.nodeValue.trim() === '') { // empty text node
                        continue;
                    }
                    *****************/
                    isInline = isInline && !BLOCKELEMENTS.test(_cnode.nodeName);
                    nodes.push(_cnode);
                }
                for(var _n = 0; _n < nodes.length; _n++) {
                    lastNode = frag.appendChild(nodes[_n]);
                }
                if( !isInline &&
                    range.collapsed &&
                    /^(|<br(|\/)>)$/i.test(range.startContainer.innerHTML) ) {
                    range.selectNode(range.startContainer);
                }
            }else{
                isInline = true;
                // paste text of some sort
                lastNode = frag = _document.createTextNode(html);
            }

            // Other Edge case - selected data spans multiple blocks.
            if(isInline){
                range.deleteContents();
            }else{ // not inline insert
                if(range.collapsed && range.startContainer !== topNode){
                    if(range.startContainer.innerHTML && range.startContainer.innerHTML.match(/^<[^>]*>$/i)){
                        // this log is to catch when innerHTML is something like `<img ...>`
                        parent = range.startContainer;
                        if(range.startOffset === 1){
                            // before single tag
                            range.setStartAfter(parent);
                            range.setEndAfter(parent);
                        }else{
                            // after single tag
                            range.setStartBefore(parent);
                            range.setEndBefore(parent);
                        }
                    }else{
                        // split element into 2 and insert block element in middle
                        if(range.startContainer.nodeType === 3 && range.startContainer.parentNode !== topNode){ // if text node
                            parent = range.startContainer.parentNode;
                            secondParent = parent.cloneNode();
                            // split the nodes into two lists - before and after, splitting the node with the selection into 2 text nodes.
                            taDOM.splitNodes(parent.childNodes, parent, secondParent, range.startContainer, range.startOffset);

                            // Escape out of the inline tags like b
                            while(!VALIDELEMENTS.test(parent.nodeName)){
                                angular.element(parent).after(secondParent);
                                parent = parent.parentNode;
                                var _lastSecondParent = secondParent;
                                secondParent = parent.cloneNode();
                                // split the nodes into two lists - before and after, splitting the node with the selection into 2 text nodes.
                                taDOM.splitNodes(parent.childNodes, parent, secondParent, _lastSecondParent);
                            }
                        }else{
                            parent = range.startContainer;
                            secondParent = parent.cloneNode();
                            taDOM.splitNodes(parent.childNodes, parent, secondParent, undefined, undefined, range.startOffset);
                        }

                        angular.element(parent).after(secondParent);
                        // put cursor to end of inserted content
                        //console.log('setStartAfter', parent);
                        range.setStartAfter(parent);
                        range.setEndAfter(parent);

                        if(/^(|<br(|\/)>)$/i.test(parent.innerHTML.trim())){
                            range.setStartBefore(parent);
                            range.setEndBefore(parent);
                            angular.element(parent).remove();
                        }
                        if(/^(|<br(|\/)>)$/i.test(secondParent.innerHTML.trim())) angular.element(secondParent).remove();
                        if(parent.nodeName.toLowerCase() === 'li'){
                            _tempFrag = _document.createDocumentFragment();
                            for(i = 0; i < frag.childNodes.length; i++){
                                element = angular.element('<li>');
                                taDOM.transferChildNodes(frag.childNodes[i], element[0]);
                                taDOM.transferNodeAttributes(frag.childNodes[i], element[0]);
                                _tempFrag.appendChild(element[0]);
                            }
                            frag = _tempFrag;
                            if(lastNode){
                                lastNode = frag.childNodes[frag.childNodes.length - 1];
                                lastNode = lastNode.childNodes[lastNode.childNodes.length - 1];
                            }
                        }
                    }
                }else{
                    range.deleteContents();
                }
            }

            range.insertNode(frag);
            if(lastNode){
                api.setSelectionToElementEnd(lastNode);
            }
        }

        /* NOT FUNCTIONAL YET
         // under Firefox, we may have a selection that needs to be normalized
         isSelectionContainerWhole_taTextElement: function (){
         var range = rangy.getSelection().getRangeAt(0);
         var container = range.commonAncestorContainer;
         if (container.nodeName.toLowerCase() === 'div' &&
         /^taTextElement/.test(container.id)) {
         // container is the whole taTextElement
         return true;
         }
         return false;
         },
         setNormalizedSelection: function (){
         var range = rangy.getSelection().getRangeAt(0);
         var container = range.commonAncestorContainer;
         console.log(range);
         console.log(container.childNodes);
         if (range.collapsed) {
         // we know what to do...
         console.log(container.childNodes[range.startOffset]);
         api.setSelectionToElementStart(container.childNodes[range.startOffset]);
         }
         },
         */
    };
    return api;
}]).service('taDOM', function(){
    var taDOM = {
        // recursive function that returns an array of angular.elements that have the passed attribute set on them
        getByAttribute: function(element, attribute){
            var resultingElements = [];
            var childNodes = element.children();
            if(childNodes.length){
                angular.forEach(childNodes, function(child){
                    resultingElements = resultingElements.concat(taDOM.getByAttribute(angular.element(child), attribute));
                });
            }
            if(element.attr(attribute) !== undefined) resultingElements.push(element);
            return resultingElements;
        },

        transferChildNodes: function(source, target){
            // clear out target
            target.innerHTML = '';
            while(source.childNodes.length > 0) target.appendChild(source.childNodes[0]);
            return target;
        },

        splitNodes: function(nodes, target1, target2, splitNode, subSplitIndex, splitIndex){
            if(!splitNode && isNaN(splitIndex)) throw new Error('taDOM.splitNodes requires a splitNode or splitIndex');
            var startNodes = document.createDocumentFragment();
            var endNodes = document.createDocumentFragment();
            var index = 0;

            while(nodes.length > 0 && (isNaN(splitIndex) || splitIndex !== index) && nodes[0] !== splitNode){
                startNodes.appendChild(nodes[0]); // this removes from the nodes array (if proper childNodes object.
                index++;
            }

            if(!isNaN(subSplitIndex) && subSplitIndex >= 0 && nodes[0]){
                startNodes.appendChild(document.createTextNode(nodes[0].nodeValue.substring(0, subSplitIndex)));
                nodes[0].nodeValue = nodes[0].nodeValue.substring(subSplitIndex);
            }
            while(nodes.length > 0) endNodes.appendChild(nodes[0]);

            taDOM.transferChildNodes(startNodes, target1);
            taDOM.transferChildNodes(endNodes, target2);
        },

        transferNodeAttributes: function(source, target){
            for(var i = 0; i < source.attributes.length; i++) target.setAttribute(source.attributes[i].name, source.attributes[i].value);
            return target;
        }
    };
    return taDOM;
});

angular.module('textAngular.validators', [])
.directive('taMaxText', function(){
    return {
        restrict: 'A',
        require: 'ngModel',
        link: function(scope, elem, attrs, ctrl){
            var max = parseInt(scope.$eval(attrs.taMaxText));
            if (isNaN(max)){
                throw('Max text must be an integer');
            }
            attrs.$observe('taMaxText', function(value){
                max = parseInt(value);
                if (isNaN(max)){
                    throw('Max text must be an integer');
                }
                if (ctrl.$dirty){
                    ctrl.$validate();
                }
            });
            ctrl.$validators.taMaxText = function(viewValue){
                var source = angular.element('<div/>');
                source.html(viewValue);
                return source.text().length <= max;
            };
        }
    };
}).directive('taMinText', function(){
    return {
        restrict: 'A',
        require: 'ngModel',
        link: function(scope, elem, attrs, ctrl){
            var min = parseInt(scope.$eval(attrs.taMinText));
            if (isNaN(min)){
                throw('Min text must be an integer');
            }
            attrs.$observe('taMinText', function(value){
                min = parseInt(value);
                if (isNaN(min)){
                    throw('Min text must be an integer');
                }
                if (ctrl.$dirty){
                    ctrl.$validate();
                }
            });
            ctrl.$validators.taMinText = function(viewValue){
                var source = angular.element('<div/>');
                source.html(viewValue);
                return !source.text().length || source.text().length >= min;
            };
        }
    };
});
angular.module('textAngular.taBind', ['textAngular.factories', 'textAngular.DOM'])
.service('_taBlankTest', [function(){
    return function(_blankVal){
        // we radically restructure this code.
        // what was here before was incredibly fragile.
        // What we do now is to check that the html is non-blank visually
        // which we check by looking at html->text
        if(!_blankVal) return true;
        // find first non-tag match - ie start of string or after tag that is not whitespace
        // var t0 = performance.now();
        // Takes a small fraction of a mSec to do this...
        var _text_ = stripHtmlToText(_blankVal);
        // var t1 = performance.now();
        // console.log('Took', (t1 - t0).toFixed(4), 'milliseconds to generate:');
        if (_text_=== '') {
            // img generates a visible item so it is not blank!
            if (/<img[^>]+>/.test(_blankVal)) {
                return false;
            }
            return true;
        } else {
            return false;
        }
    };
}])
.directive('taButton', [function(){
    return {
        link: function(scope, element, attrs){
            element.attr('unselectable', 'on');
            element.on('mousedown', function(e, eventData){
                /* istanbul ignore else: this is for catching the jqLite testing*/
                if(eventData) angular.extend(e, eventData);
                // this prevents focusout from firing on the editor when clicking toolbar buttons
                e.preventDefault();
                return false;
            });
        }
    };
}])
.directive('taBind', [
        'taSanitize', '$timeout', '$document', 'taFixChrome', 'taBrowserTag',
        'taSelection', 'taSelectableElements', 'taApplyCustomRenderers', 'taOptions',
        '_taBlankTest', '$parse', 'taDOM', 'textAngularManager',
        function(
            taSanitize, $timeout, $document, taFixChrome, taBrowserTag,
            taSelection, taSelectableElements, taApplyCustomRenderers, taOptions,
            _taBlankTest, $parse, taDOM, textAngularManager){
    // Uses for this are textarea or input with ng-model and ta-bind='text'
    // OR any non-form element with contenteditable="contenteditable" ta-bind="html|text" ng-model
    return {
        priority: 2, // So we override validators correctly
        require: ['ngModel','?ngModelOptions'],
        link: function(scope, element, attrs, controller){
            var ngModel = controller[0];
            var ngModelOptions = controller[1] || {};
            // the option to use taBind on an input or textarea is required as it will sanitize all input into it correctly.
            var _isContentEditable = element.attr('contenteditable') !== undefined && element.attr('contenteditable');
            var _isInputFriendly = _isContentEditable || element[0].tagName.toLowerCase() === 'textarea' || element[0].tagName.toLowerCase() === 'input';
            var _isReadonly = false;
            var _focussed = false;
            var _skipRender = false;
            var _disableSanitizer = attrs.taUnsafeSanitizer || taOptions.disableSanitizer;
            var _keepStyles = attrs.taKeepStyles || taOptions.keepStyles;
            var _lastKey;
            // see http://www.javascripter.net/faq/keycodes.htm for good information
            // NOTE Mute On|Off 173 (Opera MSIE Safari Chrome) 181 (Firefox)
            // BLOCKED_KEYS are special keys...
            // Tab, pause/break, CapsLock, Esc, Page Up, End, Home,
            // Left arrow, Up arrow, Right arrow, Down arrow, Insert, Delete,
            // f1, f2, f3, f4, f5, f6, f7, f8, f9, f10, f11, f12
            // NumLock, ScrollLock
            var BLOCKED_KEYS = /^(9|19|20|27|33|34|35|36|37|38|39|40|45|112|113|114|115|116|117|118|119|120|121|122|123|144|145)$/i;
            // UNDO_TRIGGER_KEYS - spaces, enter, delete, backspace, all punctuation
            // Backspace, Enter, Space, Delete, (; :) (Firefox), (= +) (Firefox),
            // Numpad +, Numpad -, (; :), (= +),
            // (, <), (- _), (. >), (/ ?), (` ~), ([ {), (\ |), (] }), (' ")
            // NOTE - Firefox: 173 = (- _) -- adding this to UNDO_TRIGGER_KEYS
            var UNDO_TRIGGER_KEYS = /^(8|13|32|46|59|61|107|109|173|186|187|188|189|190|191|192|219|220|221|222)$/i;
            var _pasteHandler;

            // defaults to the paragraph element, but we need the line-break or it doesn't allow you to type into the empty element
            // non IE is '<p><br/></p>', ie is '<p></p>' as for once IE gets it correct...
            var _defaultVal, _defaultTest;

            var _CTRL_KEY = 0x0001;
            var _META_KEY = 0x0002;
            var _ALT_KEY = 0x0004;
            var _SHIFT_KEY = 0x0008;
            // KEYCODEs we use
            var _ENTER_KEYCODE = 13;
            var _SHIFT_KEYCODE = 16;
            var _TAB_KEYCODE = 9;
            var _LEFT_ARROW_KEYCODE = 37;
            var _RIGHT_ARROW_KEYCODE = 39;
            // map events to special keys...
            // mappings is an array of maps from events to specialKeys as declared in textAngularSetup
            var _keyMappings = [
                //		ctrl/command + z
                {
                    specialKey: 'UndoKey',
                    forbiddenModifiers: _ALT_KEY + _SHIFT_KEY,
                    mustHaveModifiers: [_META_KEY + _CTRL_KEY],
                    keyCode: 90
                },
                //		ctrl/command + shift + z
                {
                    specialKey: 'RedoKey',
                    forbiddenModifiers: _ALT_KEY,
                    mustHaveModifiers: [_META_KEY + _CTRL_KEY, _SHIFT_KEY],
                    keyCode: 90
                },
                //		ctrl/command + y
                {
                    specialKey: 'RedoKey',
                    forbiddenModifiers: _ALT_KEY + _SHIFT_KEY,
                    mustHaveModifiers: [_META_KEY + _CTRL_KEY],
                    keyCode: 89
                },
                //		TabKey
                {
                    specialKey: 'TabKey',
                    forbiddenModifiers: _META_KEY + _SHIFT_KEY + _ALT_KEY + _CTRL_KEY,
                    mustHaveModifiers: [],
                    keyCode: _TAB_KEYCODE
                },
                //		shift + TabKey
                {
                    specialKey: 'ShiftTabKey',
                    forbiddenModifiers: _META_KEY + _ALT_KEY + _CTRL_KEY,
                    mustHaveModifiers: [_SHIFT_KEY],
                    keyCode: _TAB_KEYCODE
                }
            ];
            function _mapKeys(event) {
                var specialKey;
                _keyMappings.forEach(function (map){
                    if (map.keyCode === event.keyCode) {
                        var netModifiers = (event.metaKey ? _META_KEY: 0) +
                            (event.ctrlKey ? _CTRL_KEY: 0) +
                            (event.shiftKey ? _SHIFT_KEY: 0) +
                            (event.altKey ? _ALT_KEY: 0);
                        if (map.forbiddenModifiers & netModifiers) return;
                        if (map.mustHaveModifiers.every(function (modifier) { return netModifiers & modifier; })){
                            specialKey = map.specialKey;
                        }
                    }
                });
                return specialKey;
            }

            // set the default to be a paragraph value
            if(attrs.taDefaultWrap === undefined) attrs.taDefaultWrap = 'p';
            /* istanbul ignore next: ie specific test */
            if(attrs.taDefaultWrap === ''){
                _defaultVal = '';
                _defaultTest = (_browserDetect.ie === undefined)? '<div><br></div>' : (_browserDetect.ie >= 11)? '<p><br></p>' : (_browserDetect.ie <= 8)? '<P>&nbsp;</P>' : '<p>&nbsp;</p>';
            }else{
                _defaultVal = (_browserDetect.ie === undefined || _browserDetect.ie >= 11)?
                    (attrs.taDefaultWrap.toLowerCase() === 'br' ? '<BR><BR>' : '<' + attrs.taDefaultWrap + '><br></' + attrs.taDefaultWrap + '>') :
                    (_browserDetect.ie <= 8)?
                        '<' + attrs.taDefaultWrap.toUpperCase() + '></' + attrs.taDefaultWrap.toUpperCase() + '>' :
                        '<' + attrs.taDefaultWrap + '></' + attrs.taDefaultWrap + '>';
                _defaultTest = (_browserDetect.ie === undefined || _browserDetect.ie >= 11)?
                    (attrs.taDefaultWrap.toLowerCase() === 'br' ? '<br><br>' : '<' + attrs.taDefaultWrap + '><br></' + attrs.taDefaultWrap + '>') :
                    (_browserDetect.ie <= 8)?
                        '<' + attrs.taDefaultWrap.toUpperCase() + '>&nbsp;</' + attrs.taDefaultWrap.toUpperCase() + '>' :
                        '<' + attrs.taDefaultWrap + '>&nbsp;</' + attrs.taDefaultWrap + '>';
            }

            /* istanbul ignore else */
            if(!ngModelOptions.$options) ngModelOptions.$options = {}; // ng-model-options support

            var _ensureContentWrapped = function(value) {
                if (_taBlankTest(value)) return value;
                var domTest = angular.element("<div>" + value + "</div>");
                //console.log('domTest.children().length():', domTest.children().length);
                //console.log('_ensureContentWrapped', domTest.children());
                //console.log(value, attrs.taDefaultWrap);
                if (domTest.children().length === 0) {
                    // if we have a <br> and the attrs.taDefaultWrap is a <p> we need to remove the <br>
                    //value = value.replace(/<br>/i, '');
                    value = "<" + attrs.taDefaultWrap + ">" + value + "</" + attrs.taDefaultWrap + ">";
                } else {
                    var _children = domTest[0].childNodes;
                    var i;
                    var _foundBlockElement = false;
                    for (i = 0; i < _children.length; i++) {
                        if (_foundBlockElement = _children[i].nodeName.toLowerCase().match(BLOCKELEMENTS)) break;
                    }
                    if (!_foundBlockElement) {
                        value = "<" + attrs.taDefaultWrap + ">" + value + "</" + attrs.taDefaultWrap + ">";
                    }
                    else{
                        value = "";
                        for(i = 0; i < _children.length; i++){
                            var node = _children[i];
                            var nodeName = node.nodeName.toLowerCase();
                            //console.log('node#:', i, 'name:', nodeName);
                            if(nodeName === '#comment') {
                                value += '<!--' + node.nodeValue + '-->';
                            } else if(nodeName === '#text') {
                                // determine if this is all whitespace, if so, we will leave it as it is.
                                // otherwise, we will wrap it as it is
                                var text = node.textContent;
                                if (!text.trim()) {
                                    // just whitespace
                                    value += text;
                                } else {
                                    // not pure white space so wrap in <p>...</p> or whatever attrs.taDefaultWrap is set to.
                                    value += "<" + attrs.taDefaultWrap + ">" + text + "</" + attrs.taDefaultWrap + ">";
                                }
                            } else if(!nodeName.match(BLOCKELEMENTS)){
                                /* istanbul ignore  next: Doesn't seem to trigger on tests */
                                var _subVal = (node.outerHTML || node.nodeValue);
                                /* istanbul ignore else: Doesn't seem to trigger on tests, is tested though */
                                if(_subVal.trim() !== '')
                                    value += "<" + attrs.taDefaultWrap + ">" + _subVal + "</" + attrs.taDefaultWrap + ">";
                                else value += _subVal;
                            } else {
                                value += node.outerHTML;
                            }
                            //console.log(value);
                        }
                    }
                }
                //console.log(value);
                return value;
            };

            if(attrs.taPaste) {
                _pasteHandler = $parse(attrs.taPaste);
            }

            element.addClass('ta-bind');

            var _undoKeyupTimeout;

            scope['$undoManager' + (attrs.id || '')] = ngModel.$undoManager = {
                _stack: [],
                _index: 0,
                _max: 1000,
                push: function(value){
                    if((typeof value === "undefined" || value === null) ||
                        ((typeof this.current() !== "undefined" && this.current() !== null) && value === this.current())) return value;
                    if(this._index < this._stack.length - 1){
                        this._stack = this._stack.slice(0,this._index+1);
                    }
                    this._stack.push(value);
                    if(_undoKeyupTimeout) $timeout.cancel(_undoKeyupTimeout);
                    if(this._stack.length > this._max) this._stack.shift();
                    this._index = this._stack.length - 1;
                    return value;
                },
                undo: function(){
                    return this.setToIndex(this._index-1);
                },
                redo: function(){
                    return this.setToIndex(this._index+1);
                },
                setToIndex: function(index){
                    if(index < 0 || index > this._stack.length - 1){
                        return undefined;
                    }
                    this._index = index;
                    return this.current();
                },
                current: function(){
                    return this._stack[this._index];
                }
            };

            // in here we are undoing the converts used elsewhere to prevent the < > and & being displayed when they shouldn't in the code.
            var _compileHtml = function(){
                if(_isContentEditable) {
                    return element[0].innerHTML;
                }
                if(_isInputFriendly) {
                    return element.val();
                }
                throw ('textAngular Error: attempting to update non-editable taBind');
            };

            var selectorClickHandler = function(event){
                // emit the element-select event, pass the element
                scope.$emit('ta-element-select', this);
                event.preventDefault();
                return false;
            };

            //used for updating when inserting wrapped elements
            var _reApplyOnSelectorHandlers = scope['reApplyOnSelectorHandlers' + (attrs.id || '')] = function(){
                /* istanbul ignore else */
                if(!_isReadonly) angular.forEach(taSelectableElements, function(selector){
                    // check we don't apply the handler twice
                    element.find(selector)
                        .off('click', selectorClickHandler)
                        .on('click', selectorClickHandler);
                });
            };

            var _setViewValue = function(_val, triggerUndo, skipRender){
                _skipRender = skipRender || false;
                if(typeof triggerUndo === "undefined" || triggerUndo === null) triggerUndo = true && _isContentEditable; // if not contentEditable then the native undo/redo is fine
                if(typeof _val === "undefined" || _val === null) _val = _compileHtml();
                if(_taBlankTest(_val)){
                    // this avoids us from tripping the ng-pristine flag if we click in and out with out typing
                    if(ngModel.$viewValue !== '') ngModel.$setViewValue('');
                    if(triggerUndo && ngModel.$undoManager.current() !== '') ngModel.$undoManager.push('');
                }else{
                    _reApplyOnSelectorHandlers();
                    if(ngModel.$viewValue !== _val){
                        ngModel.$setViewValue(_val);
                        if(triggerUndo) ngModel.$undoManager.push(_val);
                    }
                }
                ngModel.$render();
            };

            var _setInnerHTML = function(newval){
                element[0].innerHTML = newval;
            };

            var _redoUndoTimeout;
            var _undo = scope['$undoTaBind' + (attrs.id || '')] = function(){
                /* istanbul ignore else: can't really test it due to all changes being ignored as well in readonly */
                if(!_isReadonly && _isContentEditable){
                    var content = ngModel.$undoManager.undo();
                    if(typeof content !== "undefined" && content !== null){
                        _setInnerHTML(content);
                        _setViewValue(content, false);
                        if(_redoUndoTimeout) $timeout.cancel(_redoUndoTimeout);
                        _redoUndoTimeout = $timeout(function(){
                            element[0].focus();
                            taSelection.setSelectionToElementEnd(element[0]);
                        }, 1);
                    }
                }
            };

            var _redo = scope['$redoTaBind' + (attrs.id || '')] = function(){
                /* istanbul ignore else: can't really test it due to all changes being ignored as well in readonly */
                if(!_isReadonly && _isContentEditable){
                    var content = ngModel.$undoManager.redo();
                    if(typeof content !== "undefined" && content !== null){
                        _setInnerHTML(content);
                        _setViewValue(content, false);
                        /* istanbul ignore next */
                        if(_redoUndoTimeout) $timeout.cancel(_redoUndoTimeout);
                        _redoUndoTimeout = $timeout(function(){
                            element[0].focus();
                            taSelection.setSelectionToElementEnd(element[0]);
                        }, 1);
                    }
                }
            };

            //used for updating when inserting wrapped elements
            scope['updateTaBind' + (attrs.id || '')] = function(){
                if(!_isReadonly) _setViewValue(undefined, undefined, true);
            };

            // catch DOM XSS via taSanitize
            // Sanitizing both ways is identical
            var _sanitize = function(unsafe){
                return (ngModel.$oldViewValue = taSanitize(taFixChrome(unsafe, _keepStyles), ngModel.$oldViewValue, _disableSanitizer));
            };

            // trigger the validation calls
            if(element.attr('required')) ngModel.$validators.required = function(modelValue, viewValue) {
                return !_taBlankTest(modelValue || viewValue);
            };
            // parsers trigger from the above keyup function or any other time that the viewValue is updated and parses it for storage in the ngModel
            ngModel.$parsers.push(_sanitize);
            ngModel.$parsers.unshift(_ensureContentWrapped);
            // because textAngular is bi-directional (which is awesome) we need to also sanitize values going in from the server
            ngModel.$formatters.push(_sanitize);
            ngModel.$formatters.unshift(_ensureContentWrapped);
            ngModel.$formatters.unshift(function(value){
                return ngModel.$undoManager.push(value || '');
            });

            //this code is used to update the models when data is entered/deleted
            if(_isInputFriendly){
                scope.events = {};
                if(!_isContentEditable){
                    // if a textarea or input just add in change and blur handlers, everything else is done by angulars input directive
                    element.on('change blur', scope.events.change = scope.events.blur = function(){
                        if(!_isReadonly) ngModel.$setViewValue(_compileHtml());
                    });

                    element.on('keydown', scope.events.keydown = function(event, eventData){
                        /* istanbul ignore else: this is for catching the jqLite testing*/
                        if(eventData) angular.extend(event, eventData);
                        // Reference to http://stackoverflow.com/questions/6140632/how-to-handle-tab-in-textarea
                        /* istanbul ignore else: otherwise normal functionality */
                        if(event.keyCode === _TAB_KEYCODE){ // tab was pressed
                            // get caret position/selection
                            var start = this.selectionStart;
                            var end = this.selectionEnd;

                            var value = element.val();
                            if(event.shiftKey){
                                // find \t
                                var _linebreak = value.lastIndexOf('\n', start), _tab = value.lastIndexOf('\t', start);
                                if(_tab !== -1 && _tab >= _linebreak){
                                    // set textarea value to: text before caret + tab + text after caret
                                    element.val(value.substring(0, _tab) + value.substring(_tab + 1));

                                    // put caret at right position again (add one for the tab)
                                    this.selectionStart = this.selectionEnd = start - 1;
                                }
                            }else{
                                // set textarea value to: text before caret + tab + text after caret
                                element.val(value.substring(0, start) + "\t" + value.substring(end));

                                // put caret at right position again (add one for the tab)
                                this.selectionStart = this.selectionEnd = start + 1;
                            }
                            // prevent the focus lose
                            event.preventDefault();
                        }
                    });

                    var _repeat = function(string, n){
                        var result = '';
                        for(var _n = 0; _n < n; _n++) result += string;
                        return result;
                    };

                    // add a forEach function that will work on a NodeList, etc..
                    var forEach = function (array, callback, scope) {
                        for (var i= 0; i<array.length; i++) {
                            callback.call(scope, i, array[i]);
                        }
                    };

                    // handle <ul> or <ol> nodes
                    var recursiveListFormat = function(listNode, tablevel){
                        var _html = '';
                        var _subnodes = listNode.childNodes;
                        tablevel++;
                        // tab out and add the <ul> or <ol> html piece
                        _html += _repeat('\t', tablevel-1) + listNode.outerHTML.substring(0, 4);
                        forEach(_subnodes, function (index, node) {
                            /* istanbul ignore next: browser catch */
                            var nodeName = node.nodeName.toLowerCase();
                            if (nodeName === '#comment') {
                                _html += '<!--' + node.nodeValue + '-->';
                                return;
                            }
                            if (nodeName === '#text') {
                                _html += node.textContent;
                                return;
                            }
                            /* istanbul ignore next: not tested, and this was original code -- so not wanting to possibly cause an issue, leaving it... */
                            if(!node.outerHTML) {
                                // no html to add
                                return;
                            }
                            if(nodeName === 'ul' || nodeName === 'ol') {
                                _html += '\n' + recursiveListFormat(node, tablevel);
                            }
                            else {
                                // no reformatting within this subnode, so just do the tabing...
                                _html += '\n' + _repeat('\t', tablevel) + node.outerHTML;
                            }
                        });
                        // now add on the </ol> or </ul> piece
                        _html += '\n' + _repeat('\t', tablevel-1) + listNode.outerHTML.substring(listNode.outerHTML.lastIndexOf('<'));
                        return _html;
                    };
                    // handle formating of something like:
                    // <ol><!--First comment-->
                    //  <li>Test Line 1<!--comment test list 1--></li>
                    //    <ul><!--comment ul-->
                    //      <li>Nested Line 1</li>
                    //        <!--comment between nested lines--><li>Nested Line 2</li>
                    //    </ul>
                    //  <li>Test Line 3</li>
                    // </ol>
                    ngModel.$formatters.unshift(function(htmlValue){
                        // tabulate the HTML so it looks nicer
                        //
                        // first get a list of the nodes...
                        // we do this by using the element parser...
                        //
                        // doing this -- which is simpiler -- breaks our tests...
                        //var _nodes=angular.element(htmlValue);
                        var _nodes = angular.element('<div>' + htmlValue + '</div>')[0].childNodes;
                        if(_nodes.length > 0){
                            // do the reformatting of the layout...
                            htmlValue = '';
                            forEach(_nodes, function (index, node) {
                                var nodeName = node.nodeName.toLowerCase();
                                if (nodeName === '#comment') {
                                    htmlValue += '<!--' + node.nodeValue + '-->';
                                    return;
                                }
                                if (nodeName === '#text') {
                                    htmlValue += node.textContent;
                                    return;
                                }
                                /* istanbul ignore next: not tested, and this was original code -- so not wanting to possibly cause an issue, leaving it... */
                                if(!node.outerHTML)
                                {
                                    // nothing to format!
                                    return;
                                }
                                if(htmlValue.length > 0) {
                                    // we aready have some content, so drop to a new line
                                    htmlValue += '\n';
                                }
                                if(nodeName === 'ul' || nodeName === 'ol') {
                                    // okay a set of list stuff we want to reformat in a nested way
                                    htmlValue += '' + recursiveListFormat(node, 0);
                                }
                                else {
                                    // just use the original without any additional formating
                                    htmlValue += '' + node.outerHTML;
                                }
                            });
                        }
                        return htmlValue;
                    });
                }else{
                    // all the code specific to contenteditable divs
                    var _processingPaste = false;
                    /* istanbul ignore next: phantom js cannot test this for some reason */
                    var processpaste = function(text) {
                       var _isOneNote = text!==undefined? text.match(/content=["']*OneNote.File/i): false;
                        /* istanbul ignore else: don't care if nothing pasted */
                        //console.log(text);
                        if(text && text.trim().length){
                            // test paste from word/microsoft product
                            if(text.match(/class=["']*Mso(Normal|List)/i) || text.match(/content=["']*Word.Document/i) || text.match(/content=["']*OneNote.File/i)){
                                var textFragment = text.match(/<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/i);
                                if(!textFragment) textFragment = text;
                                else textFragment = textFragment[1];
                                textFragment = textFragment.replace(/<o:p>[\s\S]*?<\/o:p>/ig, '').replace(/class=(["']|)MsoNormal(["']|)/ig, '');
                                var dom = angular.element("<div>" + textFragment + "</div>");
                                var targetDom = angular.element("<div></div>");
                                var _list = {
                                    element: null,
                                    lastIndent: [],
                                    lastLi: null,
                                    isUl: false
                                };
                                _list.lastIndent.peek = function(){
                                    var n = this.length;
                                    if (n>0) return this[n-1];
                                };
                                var _resetList = function(isUl){
                                    _list.isUl = isUl;
                                    _list.element = angular.element(isUl ? "<ul>" : "<ol>");
                                    _list.lastIndent = [];
                                    _list.lastIndent.peek = function(){
                                        var n = this.length;
                                        if (n>0) return this[n-1];
                                    };
                                    _list.lastLevelMatch = null;
                                };
                                for(var i = 0; i <= dom[0].childNodes.length; i++){
                                    if(!dom[0].childNodes[i] || dom[0].childNodes[i].nodeName === "#text"){
                                        continue;
                                    } else {
                                        var tagName = dom[0].childNodes[i].tagName.toLowerCase();
                                        if(tagName !== 'p' &&
                                            tagName !== 'ul' &&
                                            tagName !== 'h1' &&
                                            tagName !== 'h2' &&
                                            tagName !== 'h3' &&
                                            tagName !== 'h4' &&
                                            tagName !== 'h5' &&
                                            tagName !== 'h6' &&
                                            tagName !== 'table'){
                                            continue;
                                        }
                                    }
                                    var el = angular.element(dom[0].childNodes[i]);
                                    var _listMatch = (el.attr('class') || '').match(/MsoList(Bullet|Number|Paragraph)(CxSp(First|Middle|Last)|)/i);

                                    if(_listMatch){
                                        if(el[0].childNodes.length < 2 || el[0].childNodes[1].childNodes.length < 1){
                                            continue;
                                        }
                                        var isUl = _listMatch[1].toLowerCase() === 'bullet' || (_listMatch[1].toLowerCase() !== 'number' && !(/^[^0-9a-z<]*[0-9a-z]+[^0-9a-z<>]</i.test(el[0].childNodes[1].innerHTML) || /^[^0-9a-z<]*[0-9a-z]+[^0-9a-z<>]</i.test(el[0].childNodes[1].childNodes[0].innerHTML)));
                                        var _indentMatch = (el.attr('style') || '').match(/margin-left:([\-\.0-9]*)/i);
                                        var indent = parseFloat((_indentMatch)?_indentMatch[1]:0);
                                        var _levelMatch = (el.attr('style') || '').match(/mso-list:l([0-9]+) level([0-9]+) lfo[0-9+]($|;)/i);
                                        // prefers the mso-list syntax

                                        if(_levelMatch && _levelMatch[2]) indent = parseInt(_levelMatch[2]);

                                        if ((_levelMatch && (!_list.lastLevelMatch || _levelMatch[1] !== _list.lastLevelMatch[1])) || !_listMatch[3] || _listMatch[3].toLowerCase() === 'first' || (_list.lastIndent.peek() === null) || (_list.isUl !== isUl && _list.lastIndent.peek() === indent)) {
                                            _resetList(isUl);
                                            targetDom.append(_list.element);
                                        } else if (_list.lastIndent.peek() != null && _list.lastIndent.peek() < indent){
                                            _list.element = angular.element(isUl ? '<ul>' : '<ol>');
                                            _list.lastLi.append(_list.element);
                                        } else if (_list.lastIndent.peek() != null && _list.lastIndent.peek() > indent){
                                            while(_list.lastIndent.peek() != null && _list.lastIndent.peek() > indent){
                                                if(_list.element.parent()[0].tagName.toLowerCase() === 'li'){
                                                    _list.element = _list.element.parent();
                                                    continue;
                                                }else if(/[uo]l/i.test(_list.element.parent()[0].tagName.toLowerCase())){
                                                    _list.element = _list.element.parent();
                                                }else{ // else it's it should be a sibling
                                                    break;
                                                }
                                                _list.lastIndent.pop();
                                            }
                                            _list.isUl = _list.element[0].tagName.toLowerCase() === 'ul';
                                            if (isUl !== _list.isUl) {
                                                _resetList(isUl);
                                                targetDom.append(_list.element);
                                            }
                                        }

                                        _list.lastLevelMatch = _levelMatch;
                                        if(indent !== _list.lastIndent.peek()) _list.lastIndent.push(indent);
                                        _list.lastLi = angular.element('<li>');
                                        _list.element.append(_list.lastLi);
                                        _list.lastLi.html(el.html().replace(/<!(--|)\[if !supportLists\](--|)>[\s\S]*?<!(--|)\[endif\](--|)>/ig, ''));
                                        el.remove();
                                    }else{
                                        _resetList(false);
                                        targetDom.append(el);
                                    }
                                }
                                var _unwrapElement = function(node){
                                    node = angular.element(node);
                                    for(var _n = node[0].childNodes.length - 1; _n >= 0; _n--) node.after(node[0].childNodes[_n]);
                                    node.remove();
                                };

                                angular.forEach(targetDom.find('span'), function(node){
                                    node.removeAttribute('lang');
                                    if(node.attributes.length <= 0) _unwrapElement(node);
                                });
                                angular.forEach(targetDom.find('font'), _unwrapElement);

                                text = targetDom.html();
                                if(_isOneNote){
                                    text = targetDom.html() || dom.html();
                                }
                                // LF characters instead of spaces in some spots and they are replaced by '/n', so we need to just swap them to spaces
                                text = text.replace(/\n/g, ' ');
                            }else{
                                // remove unnecessary chrome insert
                                text = text.replace(/<(|\/)meta[^>]*?>/ig, '');
                                if(text.match(/<[^>]*?(ta-bind)[^>]*?>/)){
                                    // entire text-angular or ta-bind has been pasted, REMOVE AT ONCE!!
                                    if(text.match(/<[^>]*?(text-angular)[^>]*?>/)){
                                        var _el = angular.element('<div>' + text + '</div>');
                                        _el.find('textarea').remove();
                                        for(var _b = 0; _b < binds.length; _b++){
                                            var _target = binds[_b][0].parentNode.parentNode;
                                            for(var _c = 0; _c < binds[_b][0].childNodes.length; _c++){
                                                _target.parentNode.insertBefore(binds[_b][0].childNodes[_c], _target);
                                            }
                                            _target.parentNode.removeChild(_target);
                                        }
                                        text = _el.html().replace('<br class="Apple-interchange-newline">', '');
                                    }
                                }else if(text.match(/^<span/)){
                                    // in case of pasting only a span - chrome paste, remove them. THis is just some wierd formatting
                                    // if we remove the '<span class="Apple-converted-space"> </span>' here we destroy the spacing
                                    // on paste from even ourselves!
                                    if (!text.match(/<span class=(\"Apple-converted-space\"|\'Apple-converted-space\')>.<\/span>/ig)) {
                                        text = text.replace(/<(|\/)span[^>]*?>/ig, '');
                                    }
                                }
                                // Webkit on Apple tags
                                text = text.replace(/<br class="Apple-interchange-newline"[^>]*?>/ig, '').replace(/<span class="Apple-converted-space">( |&nbsp;)<\/span>/ig, '&nbsp;');
                            }

                            if (/<li(\s.*)?>/i.test(text) && /(<ul(\s.*)?>|<ol(\s.*)?>).*<li(\s.*)?>/i.test(text) === false) {
                                // insert missing parent of li element
                                text = text.replace(/<li(\s.*)?>.*<\/li(\s.*)?>/i, '<ul>$&</ul>');
                            }

                            // parse whitespace from plaintext input, starting with preceding spaces that get stripped on paste
                            text = text.replace(/^[ |\u00A0]+/gm, function (match) {
                                var result = '';
                                for (var i = 0; i < match.length; i++) {
                                    result += '&nbsp;';
                                }
                                return result;
                            }).replace(/\n|\r\n|\r/g, '<br />').replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');

                            if(_pasteHandler) text = _pasteHandler(scope, {$html: text}) || text;

                            // turn span vertical-align:super into <sup></sup>
                            text = text.replace(/<span style=("|')([^<]*?)vertical-align\s*:\s*super;?([^>]*?)("|')>([^<]+?)<\/span>/g, "<sup style='$2$3'>$5</sup>");

                            text = taSanitize(text, '', _disableSanitizer);
                            //console.log('DONE\n', text);

                            taSelection.insertHtml(text, element[0]);
                            $timeout(function(){
                                ngModel.$setViewValue(_compileHtml());
                                _processingPaste = false;
                                element.removeClass('processing-paste');
                            }, 0);
                        }else{
                            _processingPaste = false;
                            element.removeClass('processing-paste');
                        }
                    };

                    element.on('paste', scope.events.paste = function(e, eventData){
                        /* istanbul ignore else: this is for catching the jqLite testing*/
                        if(eventData) angular.extend(e, eventData);
                        if(_isReadonly || _processingPaste){
                            e.stopPropagation();
                            e.preventDefault();
                            return false;
                        }

                        // Code adapted from http://stackoverflow.com/questions/2176861/javascript-get-clipboard-data-on-paste-event-cross-browser/6804718#6804718
                        _processingPaste = true;
                        element.addClass('processing-paste');
                        var pastedContent;
                        var clipboardData = (e.originalEvent || e).clipboardData;
                        /* istanbul ignore next: Handle legacy IE paste */
                        if ( !clipboardData && window.clipboardData && window.clipboardData.getData ){
                            pastedContent = window.clipboardData.getData("Text");
                            processpaste(pastedContent);
                            e.stopPropagation();
                            e.preventDefault();
                            return false;
                        }
                        if (clipboardData && clipboardData.getData && clipboardData.types.length > 0) {// Webkit - get data from clipboard, put into editdiv, cleanup, then cancel event
                            var _types = "";
                            for(var _t = 0; _t < clipboardData.types.length; _t++){
                                _types += " " + clipboardData.types[_t];
                            }
                            /* istanbul ignore next: browser tests */
                            if (/text\/html/i.test(_types)) {
                                pastedContent = clipboardData.getData('text/html');
                            } else if (/text\/plain/i.test(_types)) {
                                pastedContent = clipboardData.getData('text/plain');
                            }
                            processpaste(pastedContent);
                            e.stopPropagation();
                            e.preventDefault();
                            return false;
                        } else {// Everything else - empty editdiv and allow browser to paste content into it, then cleanup
                            var _savedSelection = rangy.saveSelection(),
                                _tempDiv = angular.element('<div class="ta-hidden-input" contenteditable="true"></div>');
                            $document.find('body').append(_tempDiv);
                            _tempDiv[0].focus();
                            $timeout(function(){
                                // restore selection
                                rangy.restoreSelection(_savedSelection);
                                processpaste(_tempDiv[0].innerHTML);
                                element[0].focus();
                                _tempDiv.remove();
                            }, 0);
                        }
                    });
                    element.on('cut', scope.events.cut = function(e){
                        // timeout to next is needed as otherwise the paste/cut event has not finished actually changing the display
                        if(!_isReadonly) $timeout(function(){
                            ngModel.$setViewValue(_compileHtml());
                        }, 0);
                        else e.preventDefault();
                    });

                    element.on('keydown', scope.events.keydown = function(event, eventData){
                        /* istanbul ignore else: this is for catching the jqLite testing*/
                        if(eventData) angular.extend(event, eventData);
                        if (event.keyCode === _SHIFT_KEYCODE) {
                            taSelection.setStateShiftKey(true);
                        } else {
                            taSelection.setStateShiftKey(false);
                        }
                        event.specialKey = _mapKeys(event);
                        var userSpecialKey;
                        /* istanbul ignore next: difficult to test */
                        taOptions.keyMappings.forEach(function (mapping) {
                            if (event.specialKey === mapping.commandKeyCode) {
                                // taOptions has remapped this binding... so
                                // we disable our own
                                event.specialKey = undefined;
                            }
                            if (mapping.testForKey(event)) {
                                userSpecialKey = mapping.commandKeyCode;
                            }
                            if ((mapping.commandKeyCode === 'UndoKey') || (mapping.commandKeyCode === 'RedoKey')) {
                                // this is necessary to fully stop the propagation.
                                if (!mapping.enablePropagation) {
                                    event.preventDefault();
                                }
                            }
                        });
                        /* istanbul ignore next: difficult to test */
                        if (typeof userSpecialKey !== 'undefined') {
                            event.specialKey = userSpecialKey;
                        }
                        /* istanbul ignore next: difficult to test as can't seem to select */
                        if ((typeof event.specialKey !== 'undefined') && (
                                event.specialKey !== 'UndoKey' || event.specialKey !== 'RedoKey'
                            )) {
                            event.preventDefault();
                            textAngularManager.sendKeyCommand(scope, event);
                        }
                        /* istanbul ignore else: readonly check */
                        if(!_isReadonly){
                            if (event.specialKey==='UndoKey') {
                                _undo();
                                event.preventDefault();
                            }
                            if (event.specialKey==='RedoKey') {
                                _redo();
                                event.preventDefault();
                            }
                            /* istanbul ignore next: difficult to test as can't seem to select */
                            if(event.keyCode === _ENTER_KEYCODE && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey)
                            {
                                var contains = function(a, obj) {
                                    for (var i = 0; i < a.length; i++) {
                                        if (a[i] === obj) {
                                            return true;
                                        }
                                    }
                                    return false;
                                };
                                var $selection;
                                var selection = taSelection.getSelectionElement();
                                // shifted to nodeName here from tagName since it is more widely supported see: http://stackoverflow.com/questions/4878484/difference-between-tagname-and-nodename
                                if(!selection.nodeName.match(VALIDELEMENTS)) return;
                                var _new = angular.element(_defaultVal);
                                // if we are in the last element of a blockquote, or ul or ol and the element is blank
                                // we need to pull the element outside of the said type
                                var moveOutsideElements = ['blockquote', 'ul', 'ol'];
                                if (contains(moveOutsideElements, selection.parentNode.tagName.toLowerCase())) {
                                    if (/^<br(|\/)>$/i.test(selection.innerHTML.trim()) && !selection.nextSibling) {
                                        // if last element is blank, pull element outside.
                                        $selection = angular.element(selection);
                                        var _parent = $selection.parent();
                                        _parent.after(_new);
                                        $selection.remove();
                                        if (_parent.children().length === 0) _parent.remove();
                                        taSelection.setSelectionToElementStart(_new[0]);
                                        event.preventDefault();
                                    }
                                    if (/^<[^>]+><br(|\/)><\/[^>]+>$/i.test(selection.innerHTML.trim())) {
                                        $selection = angular.element(selection);
                                        $selection.after(_new);
                                        $selection.remove();
                                        taSelection.setSelectionToElementStart(_new[0]);
                                        event.preventDefault();
                                    }
                                }
                            }
                        }
                    });
                    var _keyupTimeout;
                    element.on('keyup', scope.events.keyup = function(event, eventData){
                        /* istanbul ignore else: this is for catching the jqLite testing*/
                        if(eventData) angular.extend(event, eventData);
                        taSelection.setStateShiftKey(false);	// clear the ShiftKey state
                        /* istanbul ignore next: FF specific bug fix */
                        if (event.keyCode === _TAB_KEYCODE) {
                            var _selection = taSelection.getSelection();
                            if(_selection.start.element === element[0] && element.children().length) taSelection.setSelectionToElementStart(element.children()[0]);
                            return;
                        }
                        // we do this here during the 'keyup' so that the browser has already moved the slection by one character...
                        if (event.keyCode === _LEFT_ARROW_KEYCODE && !event.shiftKey) {
                            taSelection.updateLeftArrowKey(element);
                        }
                        // we do this here during the 'keyup' so that the browser has already moved the slection by one character...
                        if (event.keyCode === _RIGHT_ARROW_KEYCODE && !event.shiftKey) {
                            taSelection.updateRightArrowKey(element);
                        }
                        if(_undoKeyupTimeout) $timeout.cancel(_undoKeyupTimeout);
                        if(!_isReadonly && !BLOCKED_KEYS.test(event.keyCode)){
                            /* istanbul ignore next: Ignore any _ENTER_KEYCODE that has ctrlKey, metaKey or alKey */
                            if (event.keyCode === _ENTER_KEYCODE && (event.ctrlKey || event.metaKey || event.altKey)) {
                                // we ignore any ENTER_	KEYCODE that is anything but plain or a shift one...
                            } else {
                                // if enter - insert new taDefaultWrap, if shift+enter insert <br/>
                                if(_defaultVal !== '' && _defaultVal !== '<BR><BR>' && event.keyCode === _ENTER_KEYCODE && !event.ctrlKey && !event.metaKey && !event.altKey){
                                    var selection = taSelection.getSelectionElement();
                                    while(!selection.nodeName.match(VALIDELEMENTS) && selection !== element[0]){
                                        selection = selection.parentNode;
                                    }
                                    if(!event.shiftKey){
                                        // new paragraph, br should be caught correctly
                                        // shifted to nodeName here from tagName since it is more widely supported see: http://stackoverflow.com/questions/4878484/difference-between-tagname-and-nodename
                                        //console.log('Enter', selection.nodeName, attrs.taDefaultWrap, selection.innerHTML.trim());
                                        if(selection.tagName.toLowerCase() !==
                                            attrs.taDefaultWrap &&
                                            selection.nodeName.toLowerCase() !== 'li' &&
                                            (selection.innerHTML.trim() === '' || selection.innerHTML.trim() === '<br>')
                                        ) {
                                            // Chrome starts with a <div><br></div> after an EnterKey
                                            // so we replace this with the _defaultVal
                                            var _new = angular.element(_defaultVal);
                                            angular.element(selection).replaceWith(_new);
                                            taSelection.setSelectionToElementStart(_new[0]);
                                        }
                                    } else {
                                        // shift + Enter
                                        var tagName = selection.tagName.toLowerCase();
                                        //console.log('Shift+Enter', selection.tagName, attrs.taDefaultWrap, selection.innerHTML.trim());
                                        // For an LI: We see: LI p ....<br><br>
                                        // For a P: We see: P p ....<br><br>
                                        // on Safari, the browser ignores the Shift+Enter and acts just as an Enter Key
                                        // For an LI: We see: LI p <br>
                                        // For a P: We see: P p <br>
                                        if((tagName === attrs.taDefaultWrap ||
                                            tagName === 'li' ||
                                            tagName === 'pre' ||
                                            tagName === 'div') &&
                                            !/.+<br><br>/.test(selection.innerHTML.trim())) {
                                            var ps = selection.previousSibling;
                                            //console.log('wrong....', ps);
                                            // we need to remove this selection and fix the previousSibling up...
                                            if (ps) {
                                                ps.innerHTML = ps.innerHTML + '<br><br>';
                                                angular.element(selection).remove();
                                                taSelection.setSelectionToElementEnd(ps);
                                            }
                                        }
                                    }
                                }
                                var val = _compileHtml();
                                if(_defaultVal !== '' && (val.trim() === '' || val.trim() === '<br>')){
                                    _setInnerHTML(_defaultVal);
                                    taSelection.setSelectionToElementStart(element.children()[0]);
                                }else if(val.substring(0, 1) !== '<' && attrs.taDefaultWrap !== ''){
                                    /* we no longer do this, since there can be comments here and white space
                                     var _savedSelection = rangy.saveSelection();
                                     val = _compileHtml();
                                     val = "<" + attrs.taDefaultWrap + ">" + val + "</" + attrs.taDefaultWrap + ">";
                                     _setInnerHTML(val);
                                     rangy.restoreSelection(_savedSelection);
                                     */
                                }
                                var triggerUndo = _lastKey !== event.keyCode && UNDO_TRIGGER_KEYS.test(event.keyCode);
                                if(_keyupTimeout) $timeout.cancel(_keyupTimeout);
                                _keyupTimeout = $timeout(function() {
                                    _setViewValue(val, triggerUndo, true);
                                }, ngModelOptions.$options.debounce || 400);
                                if(!triggerUndo) _undoKeyupTimeout = $timeout(function(){ ngModel.$undoManager.push(val); }, 250);
                                _lastKey = event.keyCode;
                            }
                        }
                    });

                    // when there is a change from a spelling correction in the browser, the only
                    // change that is seen is a 'input' and the $watch('html') sees nothing... So
                    // we added this element.on('input') to catch this change and call the _setViewValue()
                    // so the ngModel is updated and all works as it should.
                    var _inputTimeout;
                    element.on('input', function() {
                        if (_compileHtml() !== ngModel.$viewValue) {
                            // we wait a time now to allow the natural $watch('html') to handle this change
                            // and then after a 1 second delay, if there is still a difference we will do the
                            // _setViewValue() call.
                            /* istanbul ignore if: can't test */
                            if(_inputTimeout) $timeout.cancel(_inputTimeout);
                            /* istanbul ignore next: cant' test? */
                            _inputTimeout = $timeout(function() {
                                var _savedSelection = rangy.saveSelection();
                                var _val = _compileHtml();
                                if (_val !== ngModel.$viewValue) {
                                    //console.log('_setViewValue');
                                    //console.log('old:', ngModel.$viewValue);
                                    //console.log('new:', _val);
                                    _setViewValue(_val, true);
                                }
                                // if the savedSelection marker is gone at this point, we cannot restore the selection!!!
                                //console.log('rangy.restoreSelection', ngModel.$viewValue.length, _savedSelection);
                                if (ngModel.$viewValue.length !== 0) {
                                    rangy.restoreSelection(_savedSelection);
                                }
                            }, 1000);
                        }
                    });

                    element.on('blur', scope.events.blur = function(){
                        _focussed = false;
                        /* istanbul ignore else: if readonly don't update model */
                        if(!_isReadonly){
                            _setViewValue(undefined, undefined, true);
                        }else{
                            _skipRender = true; // don't redo the whole thing, just check the placeholder logic
                            ngModel.$render();
                        }
                    });

                    // Placeholders not supported on ie 8 and below
                    if(attrs.placeholder && (_browserDetect.ie > 8 || _browserDetect.ie === undefined)){
                        var rule;
                        if(attrs.id) rule = addCSSRule('#' + attrs.id + '.placeholder-text:before', 'content: "' + attrs.placeholder + '"');
                        else throw('textAngular Error: An unique ID is required for placeholders to work');

                        scope.$on('$destroy', function(){
                            removeCSSRule(rule);
                        });
                    }

                    element.on('focus', scope.events.focus = function(){
                        _focussed = true;
                        element.removeClass('placeholder-text');
                        _reApplyOnSelectorHandlers();
                    });

                    element.on('mouseup', scope.events.mouseup = function(){
                        var _selection = taSelection.getSelection();
                        if(_selection && _selection.start.element === element[0] && element.children().length) taSelection.setSelectionToElementStart(element.children()[0]);
                    });

                    // prevent propagation on mousedown in editor, see #206
                    element.on('mousedown', scope.events.mousedown = function(event, eventData){
                        /* istanbul ignore else: this is for catching the jqLite testing*/
                        if(eventData) angular.extend(event, eventData);
                        event.stopPropagation();
                    });
                }
            }

            var fileDropHandler = function(event, eventData){
                /* istanbul ignore else: this is for catching the jqLite testing*/
                if(eventData) angular.extend(event, eventData);
                // emit the drop event, pass the element, preventing should be done elsewhere
                if(!dropFired && !_isReadonly){
                    dropFired = true;
                    var dataTransfer;
                    if(event.originalEvent) dataTransfer = event.originalEvent.dataTransfer;
                    else dataTransfer = event.dataTransfer;
                    scope.$emit('ta-drop-event', this, event, dataTransfer);
                    $timeout(function(){
                        dropFired = false;
                        _setViewValue(undefined, undefined, true);
                    }, 100);
                }
            };

            var _renderTimeout;
            var _renderInProgress = false;
            // changes to the model variable from outside the html/text inputs
            ngModel.$render = function(){
                /* istanbul ignore if: Catches rogue renders, hard to replicate in tests */
                if(_renderInProgress) return;
                else _renderInProgress = true;
                // catch model being null or undefined
                var val = ngModel.$viewValue || '';
                // if the editor isn't focused it needs to be updated, otherwise it's receiving user input
                if(!_skipRender){
                    /* istanbul ignore else: in other cases we don't care */
                    if(_isContentEditable && _focussed){
                        // update while focussed
                        element.removeClass('placeholder-text');
                        /* istanbul ignore next: don't know how to test this */
                        if(_renderTimeout) $timeout.cancel(_renderTimeout);
                        _renderTimeout = $timeout(function(){
                            /* istanbul ignore if: Can't be bothered testing this... */
                            if(!_focussed){
                                element[0].focus();
                                taSelection.setSelectionToElementEnd(element.children()[element.children().length - 1]);
                            }
                            _renderTimeout = undefined;
                        }, 1);
                    }
                    if(_isContentEditable){
                        // WYSIWYG Mode
                        if(attrs.placeholder){
                            if(val === ''){
                                // blank
                                _setInnerHTML(_defaultVal);
                            }else{
                                // not-blank
                                _setInnerHTML(val);
                            }
                        }else{
                            _setInnerHTML((val === '') ? _defaultVal : val);
                        }
                        // if in WYSIWYG and readOnly we kill the use of links by clicking
                        if(!_isReadonly){
                            _reApplyOnSelectorHandlers();
                            element.on('drop', fileDropHandler);
                        }else{
                            element.off('drop', fileDropHandler);
                        }
                    }else if(element[0].tagName.toLowerCase() !== 'textarea' && element[0].tagName.toLowerCase() !== 'input'){
                        // make sure the end user can SEE the html code as a display. This is a read-only display element
                        _setInnerHTML(taApplyCustomRenderers(val));
                    }else{
                        // only for input and textarea inputs
                        element.val(val);
                    }
                }
                if(_isContentEditable && attrs.placeholder){
                    if(val === ''){
                        if(_focussed) element.removeClass('placeholder-text');
                        else element.addClass('placeholder-text');
                    }else{
                        element.removeClass('placeholder-text');
                    }
                }
                _renderInProgress = _skipRender = false;
            };

            if(attrs.taReadonly){
                //set initial value
                _isReadonly = scope.$eval(attrs.taReadonly);
                if(_isReadonly){
                    element.addClass('ta-readonly');
                    // we changed to readOnly mode (taReadonly='true')
                    if(element[0].tagName.toLowerCase() === 'textarea' || element[0].tagName.toLowerCase() === 'input'){
                        element.attr('disabled', 'disabled');
                    }
                    if(element.attr('contenteditable') !== undefined && element.attr('contenteditable')){
                        element.removeAttr('contenteditable');
                    }
                }else{
                    element.removeClass('ta-readonly');
                    // we changed to NOT readOnly mode (taReadonly='false')
                    if(element[0].tagName.toLowerCase() === 'textarea' || element[0].tagName.toLowerCase() === 'input'){
                        element.removeAttr('disabled');
                    }else if(_isContentEditable){
                        element.attr('contenteditable', 'true');
                    }
                }
                // taReadonly only has an effect if the taBind element is an input or textarea or has contenteditable='true' on it.
                // Otherwise it is readonly by default
                scope.$watch(attrs.taReadonly, function(newVal, oldVal){
                    if(oldVal === newVal) return;
                    if(newVal){
                        element.addClass('ta-readonly');
                        // we changed to readOnly mode (taReadonly='true')
                        if(element[0].tagName.toLowerCase() === 'textarea' || element[0].tagName.toLowerCase() === 'input'){
                            element.attr('disabled', 'disabled');
                        }
                        if(element.attr('contenteditable') !== undefined && element.attr('contenteditable')){
                            element.removeAttr('contenteditable');
                        }
                        // turn ON selector click handlers
                        angular.forEach(taSelectableElements, function(selector){
                            element.find(selector).on('click', selectorClickHandler);
                        });
                        element.off('drop', fileDropHandler);
                    }else{
                        element.removeClass('ta-readonly');
                        // we changed to NOT readOnly mode (taReadonly='false')
                        if(element[0].tagName.toLowerCase() === 'textarea' || element[0].tagName.toLowerCase() === 'input'){
                            element.removeAttr('disabled');
                        }else if(_isContentEditable){
                            element.attr('contenteditable', 'true');
                        }
                        // remove the selector click handlers
                        angular.forEach(taSelectableElements, function(selector){
                            element.find(selector).off('click', selectorClickHandler);
                        });
                        element.on('drop', fileDropHandler);
                    }
                    _isReadonly = newVal;
                });
            }

            // Initialise the selectableElements
            // if in WYSIWYG and readOnly we kill the use of links by clicking
            if(_isContentEditable && !_isReadonly){
                angular.forEach(taSelectableElements, function(selector){
                    element.find(selector).on('click', selectorClickHandler);
                });
                element.on('drop', fileDropHandler);
            }
        }
    };
}]);

// this global var is used to prevent multiple fires of the drop event. Needs to be global to the textAngular file.
var dropFired = false;
var textAngular = angular.module("textAngular", ['ngSanitize', 'textAngularSetup', 'textAngular.factories', 'textAngular.DOM', 'textAngular.validators', 'textAngular.taBind']); //This makes ngSanitize required

textAngular.config([function(){
    // clear taTools variable. Just catches testing and any other time that this config may run multiple times...
    angular.forEach(taTools, function(value, key){ delete taTools[key];	});
}]);

textAngular.directive("textAngular", [
    '$compile', '$timeout', 'taOptions', 'taSelection', 'taExecCommand',
    'textAngularManager', '$document', '$animate', '$log', '$q', '$parse',
    function($compile, $timeout, taOptions, taSelection, taExecCommand,
        textAngularManager, $document, $animate, $log, $q, $parse){
        return {
            require: '?ngModel',
            scope: {},
            restrict: "EA",
            priority: 2, // So we override validators correctly
            link: function(scope, element, attrs, ngModel){
                // all these vars should not be accessable outside this directive
                var _keydown, _keyup, _keypress, _mouseup, _focusin, _focusout,
                    _originalContents, _editorFunctions,
                    _serial = (attrs.serial) ? attrs.serial : Math.floor(Math.random() * 10000000000000000),
                    _taExecCommand, _resizeMouseDown, _updateSelectedStylesTimeout;
                var _resizeTimeout;

                scope._name = (attrs.name) ? attrs.name : 'textAngularEditor' + _serial;

                var oneEvent = function(_element, event, action){
                    $timeout(function(){
                        _element.one(event, action);
                    }, 100);
                };
                _taExecCommand = taExecCommand(attrs.taDefaultWrap);
                // get the settings from the defaults and add our specific functions that need to be on the scope
                angular.extend(scope, angular.copy(taOptions), {
                    // wraps the selection in the provided tag / execCommand function. Should only be called in WYSIWYG mode.
                    wrapSelection: function(command, opt, isSelectableElementTool){
                        // we restore the saved selection that was saved when focus was lost
                        /* NOT FUNCTIONAL YET */
                        /* textAngularManager.restoreFocusSelection(scope._name, scope); */
                        if(command.toLowerCase() === "undo"){
                            scope['$undoTaBindtaTextElement' + _serial]();
                        }else if(command.toLowerCase() === "redo"){
                            scope['$redoTaBindtaTextElement' + _serial]();
                        }else{
                            // catch errors like FF erroring when you try to force an undo with nothing done
                            _taExecCommand(command, false, opt, scope.defaultTagAttributes);
                            if(isSelectableElementTool){
                                // re-apply the selectable tool events
                                scope['reApplyOnSelectorHandlerstaTextElement' + _serial]();
                            }
                            // refocus on the shown display element, this fixes a display bug when using :focus styles to outline the box.
                            // You still have focus on the text/html input it just doesn't show up
                            scope.displayElements.text[0].focus();
                        }
                    },
                    showHtml: scope.$eval(attrs.taShowHtml) || false
                });
                // setup the options from the optional attributes
                if(attrs.taFocussedClass)			scope.classes.focussed = attrs.taFocussedClass;
                if(attrs.taTextEditorClass)			scope.classes.textEditor = attrs.taTextEditorClass;
                if(attrs.taHtmlEditorClass)			scope.classes.htmlEditor = attrs.taHtmlEditorClass;
                if(attrs.taDefaultTagAttributes){
                    try	{
                        //	TODO: This should use angular.merge to enhance functionality once angular 1.4 is required
                        angular.extend(scope.defaultTagAttributes, angular.fromJson(attrs.taDefaultTagAttributes));
                    } catch (error) {
                        $log.error(error);
                    }
                }
                // optional setup functions
                if(attrs.taTextEditorSetup)			scope.setup.textEditorSetup = scope.$parent.$eval(attrs.taTextEditorSetup);
                if(attrs.taHtmlEditorSetup)			scope.setup.htmlEditorSetup = scope.$parent.$eval(attrs.taHtmlEditorSetup);
                // optional fileDropHandler function
                if(attrs.taFileDrop)				scope.fileDropHandler = scope.$parent.$eval(attrs.taFileDrop);
                else								scope.fileDropHandler = scope.defaultFileDropHandler;

                _originalContents = element[0].innerHTML;
                // clear the original content
                element[0].innerHTML = '';

                // Setup the HTML elements as variable references for use later
                scope.displayElements = {
                    // we still need the hidden input even with a textarea as the textarea may have invalid/old input in it,
                    // wheras the input will ALLWAYS have the correct value.
                    forminput: angular.element("<input type='hidden' tabindex='-1' style='display: none;'>"),
                    html: angular.element("<textarea></textarea>"),
                    text: angular.element("<div></div>"),
                    // other toolbased elements
                    scrollWindow: angular.element("<div class='ta-scroll-window'></div>"),
                    popover: angular.element('<div class="popover fade bottom" style="max-width: none; width: 305px;"></div>'),
                    popoverArrow: angular.element('<div class="arrow"></div>'),
                    popoverContainer: angular.element('<div class="popover-content"></div>'),
                    resize: {
                        overlay: angular.element('<div class="ta-resizer-handle-overlay"></div>'),
                        background: angular.element('<div class="ta-resizer-handle-background"></div>'),
                        anchors: [
                            angular.element('<div class="ta-resizer-handle-corner ta-resizer-handle-corner-tl"></div>'),
                            angular.element('<div class="ta-resizer-handle-corner ta-resizer-handle-corner-tr"></div>'),
                            angular.element('<div class="ta-resizer-handle-corner ta-resizer-handle-corner-bl"></div>'),
                            angular.element('<div class="ta-resizer-handle-corner ta-resizer-handle-corner-br"></div>')
                        ],
                        info: angular.element('<div class="ta-resizer-handle-info"></div>')
                    }
                };

                // Setup the popover
                scope.displayElements.popover.append(scope.displayElements.popoverArrow);
                scope.displayElements.popover.append(scope.displayElements.popoverContainer);
                scope.displayElements.scrollWindow.append(scope.displayElements.popover);

                scope.displayElements.popover.on('mousedown', function(e, eventData){
                    /* istanbul ignore else: this is for catching the jqLite testing*/
                    if(eventData) angular.extend(e, eventData);
                    // this prevents focusout from firing on the editor when clicking anything in the popover
                    e.preventDefault();
                    return false;
                });

                /* istanbul ignore next: popover resize and scroll events handled */
                scope.handlePopoverEvents = function() {
                    if (scope.displayElements.popover.css('display')==='block') {
                        if(_resizeTimeout) $timeout.cancel(_resizeTimeout);
                        _resizeTimeout = $timeout(function() {
                            //console.log('resize', scope.displayElements.popover.css('display'));
                            scope.reflowPopover(scope.resizeElement);
                            scope.reflowResizeOverlay(scope.resizeElement);
                        }, 100);
                    }
                };

                /* istanbul ignore next: browser resize check */
                angular.element(window).on('resize', scope.handlePopoverEvents);

                /* istanbul ignore next: browser scroll check */
                angular.element(window).on('scroll', scope.handlePopoverEvents);

                // we want to know if a given node has a scrollbar!
                // credit to lotif on http://stackoverflow.com/questions/4880381/check-whether-html-element-has-scrollbars
                var isScrollable = function(node) {
                    var cs;
                    var _notScrollable = {
                        vertical: false,
                        horizontal: false,
                    };
                    try {
                        cs = window.getComputedStyle(node);
                        if (cs === null) {
                            return _notScrollable;
                        }
                    } catch (e) {
                        /* istanbul ignore next: error handler */
                        return _notScrollable;
                    }
                    var overflowY = cs['overflow-y'];
                    var overflowX = cs['overflow-x'];
                    return {
                        vertical: (overflowY === 'scroll' || overflowY === 'auto') &&
                                    /* istanbul ignore next: not tested */
                                    node.scrollHeight > node.clientHeight,
                        horizontal: (overflowX === 'scroll' || overflowX === 'auto') &&
                                    /* istanbul ignore next: not tested */
                                    node.scrollWidth > node.clientWidth,
                    };
                };

                // getScrollTop
                //
                // we structure this so that it can climb the parents of the _el and when it finds
                // one with scrollbars, it adds an EventListener, so that no matter how the
                // DOM is structured in the user APP, if there is a scrollbar not as part of the
                // ta-scroll-window, we will still capture the 'scroll' events...
                // and handle the scroll event properly and do the resize, etc.
                //
                scope.getScrollTop = function (_el, bAddListener) {
                    var scrollTop = _el.scrollTop;
                    if (typeof scrollTop === 'undefined') {
                        scrollTop = 0;
                    }
                    /* istanbul ignore next: triggered only if has scrollbar */
                    if (bAddListener && isScrollable(_el).vertical) {
                        // remove element eventListener
                        _el.removeEventListener('scroll', scope._scrollListener, false);
                        _el.addEventListener('scroll', scope._scrollListener, false);
                    }
                    /* istanbul ignore next: triggered only if has scrollbar and scrolled */
                    if (scrollTop !== 0) {
                        return { node:_el.nodeName, top:scrollTop };
                    }
                    /* istanbul ignore else: catches only if no scroll */
                    if (_el.parentNode) {
                        return scope.getScrollTop(_el.parentNode, bAddListener);
                    } else {
                        return { node:'<none>', top:0 };
                    }
                };

                // define the popover show and hide functions
                scope.showPopover = function(_el){
                    scope.getScrollTop(scope.displayElements.scrollWindow[0], true);
                    scope.displayElements.popover.css('display', 'block');
                    // we must use a $timeout here, or the css change to the
                    // displayElements.resize.overlay will not take!!!
                    // WHY???
                    $timeout(function() {
                        scope.displayElements.resize.overlay.css('display', 'block');
                    });
                    scope.resizeElement = _el;
                    scope.reflowPopover(_el);
                    $animate.addClass(scope.displayElements.popover, 'in');
                    oneEvent($document.find('body'), 'click keyup', function(){scope.hidePopover();});
                };

                /* istanbul ignore next: browser scroll event handler */
                scope._scrollListener = function (e, eventData){
                    scope.handlePopoverEvents();
                };

                scope.reflowPopover = function(_el){
                    var scrollTop = scope.getScrollTop(scope.displayElements.scrollWindow[0], false);
                    var spaceAboveImage = _el[0].offsetTop-scrollTop.top;
                    //var spaceBelowImage = scope.displayElements.text[0].offsetHeight - _el[0].offsetHeight - spaceAboveImage;
                    //console.log(spaceAboveImage, spaceBelowImage);

                    /* istanbul ignore if: catches only if near bottom of editor */
                    if(spaceAboveImage < 51) {
                        scope.displayElements.popover.css('top', _el[0].offsetTop + _el[0].offsetHeight + scope.displayElements.scrollWindow[0].scrollTop + 'px');
                        scope.displayElements.popover.removeClass('top').addClass('bottom');
                    } else {
                        scope.displayElements.popover.css('top', _el[0].offsetTop - 54 + scope.displayElements.scrollWindow[0].scrollTop + 'px');
                        scope.displayElements.popover.removeClass('bottom').addClass('top');
                    }
                    var _maxLeft = scope.displayElements.text[0].offsetWidth - scope.displayElements.popover[0].offsetWidth;
                    var _targetLeft = _el[0].offsetLeft + (_el[0].offsetWidth / 2.0) - (scope.displayElements.popover[0].offsetWidth / 2.0);
                    var _rleft = Math.max(0, Math.min(_maxLeft, _targetLeft));
                    var _marginLeft = (Math.min(_targetLeft, (Math.max(0, _targetLeft - _maxLeft))) - 11);
                    _rleft += window.scrollX;
                    _marginLeft -= window.scrollX;
                    scope.displayElements.popover.css('left', _rleft + 'px');
                    scope.displayElements.popoverArrow.css('margin-left', _marginLeft + 'px');
                };
                scope.hidePopover = function(){
                    scope.displayElements.popover.css('display', 'none');
                    scope.displayElements.popoverContainer.attr('style', '');
                    scope.displayElements.popoverContainer.attr('class', 'popover-content');
                    scope.displayElements.popover.removeClass('in');
                    scope.displayElements.resize.overlay.css('display', 'none');
                };

                // setup the resize overlay
                scope.displayElements.resize.overlay.append(scope.displayElements.resize.background);
                angular.forEach(scope.displayElements.resize.anchors, function(anchor){ scope.displayElements.resize.overlay.append(anchor);});
                scope.displayElements.resize.overlay.append(scope.displayElements.resize.info);
                scope.displayElements.scrollWindow.append(scope.displayElements.resize.overlay);

                // A click event on the resize.background will now shift the focus to the editor
                /* istanbul ignore next: click on the resize.background to focus back to editor */
                scope.displayElements.resize.background.on('click', function(e) {
                    scope.displayElements.text[0].focus();
                });

                // define the show and hide events
                scope.reflowResizeOverlay = function(_el){
                    _el = angular.element(_el)[0];
                    scope.displayElements.resize.overlay.css({
                        'display': 'block',
                        'left': _el.offsetLeft - 5 + 'px',
                        'top': _el.offsetTop - 5 + 'px',
                        'width': _el.offsetWidth + 10 + 'px',
                        'height': _el.offsetHeight + 10 + 'px'
                    });
                    scope.displayElements.resize.info.text(_el.offsetWidth + ' x ' + _el.offsetHeight);
                };
                /* istanbul ignore next: pretty sure phantomjs won't test this */
                scope.showResizeOverlay = function(_el){
                    var _body = $document.find('body');
                    _resizeMouseDown = function(event){
                        var startPosition = {
                            width: parseInt(_el.attr('width')),
                            height: parseInt(_el.attr('height')),
                            x: event.clientX,
                            y: event.clientY
                        };
                        if(startPosition.width === undefined || isNaN(startPosition.width)) startPosition.width = _el[0].offsetWidth;
                        if(startPosition.height === undefined || isNaN(startPosition.height)) startPosition.height = _el[0].offsetHeight;
                        scope.hidePopover();
                        var ratio = startPosition.height / startPosition.width;
                        var mousemove = function(event){
                            // calculate new size
                            var pos = {
                                x: Math.max(0, startPosition.width + (event.clientX - startPosition.x)),
                                y: Math.max(0, startPosition.height + (event.clientY - startPosition.y))
                            };

                            // DEFAULT: the aspect ratio is not locked unless the Shift key is pressed.
                            //
                            // attribute: ta-resize-force-aspect-ratio -- locks resize into maintaing the aspect ratio
                            var bForceAspectRatio = (attrs.taResizeForceAspectRatio !== undefined);
                            // attribute: ta-resize-maintain-aspect-ratio=true causes the space ratio to remain locked
                            // unless the Shift key is pressed
                            var bFlipKeyBinding = attrs.taResizeMaintainAspectRatio;
                            var bKeepRatio =  bForceAspectRatio || (bFlipKeyBinding && !event.shiftKey);
                            if(bKeepRatio) {
                                var newRatio = pos.y / pos.x;
                                pos.x = ratio > newRatio ? pos.x : pos.y / ratio;
                                pos.y = ratio > newRatio ? pos.x * ratio : pos.y;
                            }
                            var el = angular.element(_el);
                            function roundedMaxVal(val) {
                                return Math.round(Math.max(0, val));
                            }
                            el.css('height', roundedMaxVal(pos.y) + 'px');
                            el.css('width', roundedMaxVal(pos.x) + 'px');

                            // reflow the popover tooltip
                            scope.reflowResizeOverlay(_el);
                        };
                        _body.on('mousemove', mousemove);
                        oneEvent(_body, 'mouseup', function(event){
                            event.preventDefault();
                            event.stopPropagation();
                            _body.off('mousemove', mousemove);
                            // at this point, we need to force the model to update! since the css has changed!
                            // this fixes bug: #862 - we now hide the popover -- as this seems more consitent.
                            // there are still issues under firefox, the window does not repaint. -- not sure
                            // how best to resolve this, but clicking anywhere works.
                            scope.$apply(function (){
                                scope.hidePopover();
                                scope.updateTaBindtaTextElement();
                            }, 100);
                        });
                        event.stopPropagation();
                        event.preventDefault();
                    };

                    scope.displayElements.resize.anchors[3].off('mousedown');
                    scope.displayElements.resize.anchors[3].on('mousedown', _resizeMouseDown);

                    scope.reflowResizeOverlay(_el);
                    oneEvent(_body, 'click', function(){scope.hideResizeOverlay();});
                };
                /* istanbul ignore next: pretty sure phantomjs won't test this */
                scope.hideResizeOverlay = function(){
                    scope.displayElements.resize.anchors[3].off('mousedown', _resizeMouseDown);
                    scope.displayElements.resize.overlay.css('display', 'none');
                };

                // allow for insertion of custom directives on the textarea and div
                scope.setup.htmlEditorSetup(scope.displayElements.html);
                scope.setup.textEditorSetup(scope.displayElements.text);
                scope.displayElements.html.attr({
                    'id': 'taHtmlElement' + _serial,
                    'ng-show': 'showHtml',
                    'ta-bind': 'ta-bind',
                    'ng-model': 'html',
                    'ng-model-options': element.attr('ng-model-options')
                });
                scope.displayElements.text.attr({
                    'id': 'taTextElement' + _serial,
                    'contentEditable': 'true',
                    'ta-bind': 'ta-bind',
                    'ng-model': 'html',
                    'ng-model-options': element.attr('ng-model-options')
                });
                scope.displayElements.scrollWindow.attr({'ng-hide': 'showHtml'});
                if(attrs.taDefaultWrap) {
                    // taDefaultWrap is only applied to the text and not the html view
                    scope.displayElements.text.attr('ta-default-wrap', attrs.taDefaultWrap);
                }

                if(attrs.taUnsafeSanitizer){
                    scope.displayElements.text.attr('ta-unsafe-sanitizer', attrs.taUnsafeSanitizer);
                    scope.displayElements.html.attr('ta-unsafe-sanitizer', attrs.taUnsafeSanitizer);
                }

                if(attrs.taKeepStyles){
                    scope.displayElements.text.attr('ta-keep-styles', attrs.taKeepStyles);
                    scope.displayElements.html.attr('ta-keep-styles', attrs.taKeepStyles);
                }

                // add the main elements to the origional element
                scope.displayElements.scrollWindow.append(scope.displayElements.text);
                element.append(scope.displayElements.scrollWindow);
                element.append(scope.displayElements.html);

                scope.displayElements.forminput.attr('name', scope._name);
                element.append(scope.displayElements.forminput);

                if(attrs.tabindex){
                    element.removeAttr('tabindex');
                    scope.displayElements.text.attr('tabindex', attrs.tabindex);
                    scope.displayElements.html.attr('tabindex', attrs.tabindex);
                }

                if (attrs.placeholder) {
                    scope.displayElements.text.attr('placeholder', attrs.placeholder);
                    scope.displayElements.html.attr('placeholder', attrs.placeholder);
                }

                if(attrs.taDisabled){
                    scope.displayElements.text.attr('ta-readonly', 'disabled');
                    scope.displayElements.html.attr('ta-readonly', 'disabled');
                    scope.disabled = scope.$parent.$eval(attrs.taDisabled);
                    scope.$parent.$watch(attrs.taDisabled, function(newVal){
                        scope.disabled = newVal;
                        if(scope.disabled){
                            element.addClass(scope.classes.disabled);
                        }else{
                            element.removeClass(scope.classes.disabled);
                        }
                    });
                }

                if(attrs.taPaste){
                    scope._pasteHandler = function(_html){
                        return $parse(attrs.taPaste)(scope.$parent, {$html: _html});
                    };
                    scope.displayElements.text.attr('ta-paste', '_pasteHandler($html)');
                }

                // compile the scope with the text and html elements only - if we do this with the main element it causes a compile loop
                $compile(scope.displayElements.scrollWindow)(scope);
                $compile(scope.displayElements.html)(scope);

                scope.updateTaBindtaTextElement = scope['updateTaBindtaTextElement' + _serial];
                scope.updateTaBindtaHtmlElement = scope['updateTaBindtaHtmlElement' + _serial];

                // add the classes manually last
                element.addClass("ta-root");
                scope.displayElements.scrollWindow.addClass("ta-text ta-editor " + scope.classes.textEditor);
                scope.displayElements.html.addClass("ta-html ta-editor " + scope.classes.htmlEditor);

                var testAndSet = function(choice, beforeState) {
                    /* istanbul ignore next: this is only here because of a bug in rangy where rangy.saveSelection() has cleared the state */
                    if (beforeState !== $document[0].queryCommandState(choice)) {
                        $document[0].execCommand(choice, false, null);
                    }
                };
                // used in the toolbar actions
                scope._actionRunning = false;
                var _savedSelection = false;
                scope.startAction = function(){
                    var _beforeStateBold = false;
                    var _beforeStateItalic = false;
                    var _beforeStateUnderline = false;
                    var _beforeStateStrikethough = false;
                    scope._actionRunning = true;
                    _beforeStateBold = $document[0].queryCommandState('bold');
                    _beforeStateItalic = $document[0].queryCommandState('italic');
                    _beforeStateUnderline = $document[0].queryCommandState('underline');
                    _beforeStateStrikethough = $document[0].queryCommandState('strikeThrough');
                    //console.log('B', _beforeStateBold, 'I', _beforeStateItalic, '_', _beforeStateUnderline, 'S', _beforeStateStrikethough);
                    // if rangy library is loaded return a function to reload the current selection
                    _savedSelection = rangy.saveSelection();
                    // rangy.saveSelection() clear the state of bold, italic, underline, strikethrough
                    // so we reset them here....!!!
                    // this fixes bugs #423, #1129, #1105, #693 which are actually rangy bugs!
                    testAndSet('bold', _beforeStateBold);
                    testAndSet('italic', _beforeStateItalic);
                    testAndSet('underline', _beforeStateUnderline);
                    testAndSet('strikeThrough', _beforeStateStrikethough);
                    //console.log('B', $document[0].queryCommandState('bold'), 'I', $document[0].queryCommandState('italic'), '_', $document[0].queryCommandState('underline'), 'S', $document[0].queryCommandState('strikeThrough') );
                    return function(){
                        if(_savedSelection) rangy.restoreSelection(_savedSelection);
                        // perhaps if we restore the selections here, we would do better overall???
                        // BUT what we do above does well in 90% of the cases...
                    };
                };
                scope.endAction = function(){
                    scope._actionRunning = false;
                    if(_savedSelection){
                        if(scope.showHtml){
                            scope.displayElements.html[0].focus();
                        }else{
                            scope.displayElements.text[0].focus();
                        }
                        // rangy.restoreSelection(_savedSelection);
                        rangy.removeMarkers(_savedSelection);
                    }
                    _savedSelection = false;
                    scope.updateSelectedStyles();
                    // only update if in text or WYSIWYG mode
                    if(!scope.showHtml) scope['updateTaBindtaTextElement' + _serial]();
                };

                // note that focusout > focusin is called everytime we click a button - except bad support: http://www.quirksmode.org/dom/events/blurfocus.html
                // cascades to displayElements.text and displayElements.html automatically.
                _focusin = function(e){
                    scope.focussed = true;
                    element.addClass(scope.classes.focussed);
/*******  NOT FUNCTIONAL YET
                    if (e.target.id === 'taTextElement' + _serial) {
                        console.log('_focusin taTextElement');
                        // we only do this if NOT focussed
                        textAngularManager.restoreFocusSelection(scope._name);
                    }
*******/
                    _editorFunctions.focus();
                    element.triggerHandler('focus');
                    // we call editorScope.updateSelectedStyles() here because we want the toolbar to be focussed
                    // as soon as we have focus.  Otherwise this only happens on mousedown or keydown etc...
                    /* istanbul ignore else: don't run if already running */
                    if(scope.updateSelectedStyles && !scope._bUpdateSelectedStyles){
                        // we don't set editorScope._bUpdateSelectedStyles here, because we do not want the
                        // updateSelectedStyles() to run twice which it will do after 200 msec if we have
                        // set editorScope._bUpdateSelectedStyles
                        //
                        // WOW, normally I would do a scope.$apply here, but this causes ERRORs when doing tests!
                        $timeout(function () {
                            scope.updateSelectedStyles();
                        }, 0);
                    }
                };
                scope.displayElements.html.on('focus', _focusin);
                scope.displayElements.text.on('focus', _focusin);
                _focusout = function(e){
                    /****************** NOT FUNCTIONAL YET
                    try {
                        var _s = rangy.getSelection();
                        if (_s) {
                            // we save the selection when we loose focus so that if do a wrapSelection, the
                            // apropriate selection in the editor is restored before action.
                            var _savedFocusRange = rangy.saveRange(_s.getRangeAt(0));
                            textAngularManager.saveFocusSelection(scope._name, _savedFocusRange);
                        }
                    } catch(error) { }
                    *****************/
                    // if we are NOT runnig an action and have NOT focussed again on the text etc then fire the blur events
                    if(!scope._actionRunning &&
                        $document[0].activeElement !== scope.displayElements.html[0] &&
                        $document[0].activeElement !== scope.displayElements.text[0])
                    {
                        element.removeClass(scope.classes.focussed);
                        _editorFunctions.unfocus();
                        // to prevent multiple apply error defer to next seems to work.
                        $timeout(function(){
                            scope._bUpdateSelectedStyles = false;
                            element.triggerHandler('blur');
                            scope.focussed = false;
                        }, 0);
                    }
                    e.preventDefault();
                    return false;
                };
                scope.displayElements.html.on('blur', _focusout);
                scope.displayElements.text.on('blur', _focusout);

                scope.displayElements.text.on('paste', function(event){
                    element.triggerHandler('paste', event);
                });

                // Setup the default toolbar tools, this way allows the user to add new tools like plugins.
                // This is on the editor for future proofing if we find a better way to do this.
                scope.queryFormatBlockState = function(command){
                    // $document[0].queryCommandValue('formatBlock') errors in Firefox if we call this when focussed on the textarea
                    return !scope.showHtml && command.toLowerCase() === $document[0].queryCommandValue('formatBlock').toLowerCase();
                };
                scope.queryCommandState = function(command){
                    // $document[0].queryCommandValue('formatBlock') errors in Firefox if we call this when focussed on the textarea
                    return (!scope.showHtml) ? $document[0].queryCommandState(command) : '';
                };
                scope.switchView = function(){
                    scope.showHtml = !scope.showHtml;
                    $animate.enabled(false, scope.displayElements.html);
                    $animate.enabled(false, scope.displayElements.text);
                    //Show the HTML view
                    /* istanbul ignore next: ngModel exists check */
/* THIS is not the correct thing to do, here....
   The ngModel is correct, but it is not formatted as the user as done it...
                    var _model;
                    if (ngModel) {
                        _model = ngModel.$viewValue;
                    } else {
                        _model = scope.html;
                    }
                    var _html = scope.displayElements.html[0].value;
                    if (getDomFromHtml(_html).childElementCount !== getDomFromHtml(_model).childElementCount) {
                        // the model and the html do not agree
                        // they can get out of sync and when they do, we correct that here...
                        scope.displayElements.html.val(_model);
                    }
*/
                    if(scope.showHtml){
                        //defer until the element is visible
                        $timeout(function(){
                            $animate.enabled(true, scope.displayElements.html);
                            $animate.enabled(true, scope.displayElements.text);
                            // [0] dereferences the DOM object from the angular.element
                            return scope.displayElements.html[0].focus();
                        }, 100);
                    }else{
                        //Show the WYSIWYG view
                        //defer until the element is visible
                        $timeout(function(){
                            $animate.enabled(true, scope.displayElements.html);
                            $animate.enabled(true, scope.displayElements.text);
                            // [0] dereferences the DOM object from the angular.element
                            return scope.displayElements.text[0].focus();
                        }, 100);
                    }
                };

                // changes to the model variable from outside the html/text inputs
                // if no ngModel, then the only input is from inside text-angular
                if(attrs.ngModel){
                    var _firstRun = true;
                    ngModel.$render = function(){
                        if(_firstRun){
                            // we need this firstRun to set the originalContents otherwise it gets overrided by the setting of ngModel to undefined from NaN
                            _firstRun = false;
                            // if view value is null or undefined initially and there was original content, set to the original content
                            var _initialValue = scope.$parent.$eval(attrs.ngModel);
                            if((_initialValue === undefined || _initialValue === null) && (_originalContents && _originalContents !== '')){
                                // on passing through to taBind it will be sanitised
                                ngModel.$setViewValue(_originalContents);
                            }
                        }
                        scope.displayElements.forminput.val(ngModel.$viewValue);
                        // if the editors aren't focused they need to be updated, otherwise they are doing the updating
                        scope.html = ngModel.$viewValue || '';
                    };
                    // trigger the validation calls
                    if(element.attr('required')) ngModel.$validators.required = function(modelValue, viewValue) {
                        var value = modelValue || viewValue;
                        return !(!value || value.trim() === '');
                    };
                }else{
                    // if no ngModel then update from the contents of the origional html.
                    scope.displayElements.forminput.val(_originalContents);
                    scope.html = _originalContents;
                }

                // changes from taBind back up to here
                scope.$watch('html', function(newValue, oldValue){
                    if(newValue !== oldValue){
                        if(attrs.ngModel && ngModel.$viewValue !== newValue) {
                            ngModel.$setViewValue(newValue);
                        }
                        scope.displayElements.forminput.val(newValue);
                    }
                });

                if(attrs.taTargetToolbars) {
                    _editorFunctions = textAngularManager.registerEditor(scope._name, scope, attrs.taTargetToolbars.split(','));
                }
                else{
                    var _toolbar = angular.element('<div text-angular-toolbar name="textAngularToolbar' + _serial + '">');
                    // passthrough init of toolbar options
                    if(attrs.taToolbar)						_toolbar.attr('ta-toolbar', attrs.taToolbar);
                    if(attrs.taToolbarClass)				_toolbar.attr('ta-toolbar-class', attrs.taToolbarClass);
                    if(attrs.taToolbarGroupClass)			_toolbar.attr('ta-toolbar-group-class', attrs.taToolbarGroupClass);
                    if(attrs.taToolbarButtonClass)			_toolbar.attr('ta-toolbar-button-class', attrs.taToolbarButtonClass);
                    if(attrs.taToolbarActiveButtonClass)	_toolbar.attr('ta-toolbar-active-button-class', attrs.taToolbarActiveButtonClass);
                    if(attrs.taFocussedClass)				_toolbar.attr('ta-focussed-class', attrs.taFocussedClass);

                    element.prepend(_toolbar);
                    $compile(_toolbar)(scope.$parent);
                    _editorFunctions = textAngularManager.registerEditor(scope._name, scope, ['textAngularToolbar' + _serial]);
                }

                scope.$on('$destroy', function(){
                    textAngularManager.unregisterEditor(scope._name);
                    angular.element(window).off('blur');
                    angular.element(window).off('resize', scope.handlePopoverEvents);
                    angular.element(window).off('scroll', scope.handlePopoverEvents);
                });

                // catch element select event and pass to toolbar tools
                scope.$on('ta-element-select', function(event, element){
                    if(_editorFunctions.triggerElementSelect(event, element)){
                        scope['reApplyOnSelectorHandlerstaTextElement' + _serial]();
                    }
                });

/******************* no working fully
                var distanceFromPoint = function (px, py, x, y) {
                    return Math.sqrt((px-x)*(px-x)+(py-y)*(py-y));
                };
                // because each object is a rectangle and we have a single point,
                // we need to give priority if the point is inside the rectangle
                var getPositionDistance = function(el, x, y) {
                    var range = document.createRange();
                    range.selectNode(el);
                    var rect = range.getBoundingClientRect();
                    console.log(el, rect);
                    range.detach();
                    var bcr = rect;
                    // top left
                    var d1 = distanceFromPoint(bcr.left, bcr.top, x, y);
                    // bottom left
                    var d2 = distanceFromPoint(bcr.left, bcr.bottom, x, y);
                    // top right
                    var d3 = distanceFromPoint(bcr.right, bcr.top, x, y);
                    // bottom right
                    var d4 = distanceFromPoint(bcr.right, bcr.bottom, x, y);
                    return Math.min(d1, d2, d3, d4);
                };
                var findClosest = function(el, minElement, maxDistance, x, y) {
                    var _d=0;
                    for (var i = 0; i < el.childNodes.length; i++) {
                        var _n = el.childNodes[i];
                        if (!_n.childNodes.length) {
                            _d = getPositionDistance(_n, x, y);
                            //console.log(_n, _n.childNodes, _d);
                            if (_d < maxDistance) {
                                maxDistance = _d;
                                minElement = _n;
                            }
                        }
                        var res = findClosest(_n, minElement, maxDistance, x, y);
                        if (res.max < maxDistance) {
                            maxDistance = res.max;
                            minElement = res.min;
                        }
                    }
                    return { max: maxDistance, min: minElement };
                };
                var getClosestElement = function (el, x, y) {
                    return findClosest(el, null, 12341234124, x, y);
                };
****************/

                scope.$on('ta-drop-event', function(event, element, dropEvent, dataTransfer){
                    if(dataTransfer && dataTransfer.files && dataTransfer.files.length > 0){
                        scope.displayElements.text[0].focus();
                        // we must set the location of the drop!
                        //console.log(dropEvent.clientX, dropEvent.clientY, dropEvent.target);
                        taSelection.setSelectionToElementEnd(dropEvent.target);
                        angular.forEach(dataTransfer.files, function(file){
                            // taking advantage of boolean execution, if the fileDropHandler returns true, nothing else after it is executed
                            // If it is false then execute the defaultFileDropHandler if the fileDropHandler is NOT the default one
                            // Once one of these has been executed wrap the result as a promise, if undefined or variable update the taBind, else we should wait for the promise
                            try{
                                $q.when(scope.fileDropHandler(file, scope.wrapSelection) ||
                                    (scope.fileDropHandler !== scope.defaultFileDropHandler &&
                                    $q.when(scope.defaultFileDropHandler(file, scope.wrapSelection)))).then(function(){
                                        scope['updateTaBindtaTextElement' + _serial]();
                                    });
                            }catch(error){
                                $log.error(error);
                            }
                        });
                        dropEvent.preventDefault();
                        dropEvent.stopPropagation();
                    /* istanbul ignore else, the updates if moved text */
                    }else{
                        $timeout(function(){
                            scope['updateTaBindtaTextElement' + _serial]();
                        }, 0);
                    }
                });

                // the following is for applying the active states to the tools that support it
                scope._bUpdateSelectedStyles = false;
                /* istanbul ignore next: browser window/tab leave check */
                angular.element(window).on('blur', function(){
                    scope._bUpdateSelectedStyles = false;
                    scope.focussed = false;
                });
                // loop through all the tools polling their activeState function if it exists
                scope.updateSelectedStyles = function(){
                    var _selection;
                    /* istanbul ignore next: This check is to ensure multiple timeouts don't exist */
                    if(_updateSelectedStylesTimeout) $timeout.cancel(_updateSelectedStylesTimeout);
                    // test if the common element ISN'T the root ta-text node
                    if((_selection = taSelection.getSelectionElement()) !== undefined && _selection.parentNode !== scope.displayElements.text[0]){
                        _editorFunctions.updateSelectedStyles(angular.element(_selection));
                    }else _editorFunctions.updateSelectedStyles();
                    // used to update the active state when a key is held down, ie the left arrow
                    /* istanbul ignore else: browser only check */
                    if(scope._bUpdateSelectedStyles) _updateSelectedStylesTimeout = $timeout(scope.updateSelectedStyles, 200);
                };
                // start updating on keydown
                _keydown = function(){
                    /* istanbul ignore next: ie catch */
                    if(!scope.focussed){
                        scope._bUpdateSelectedStyles = false;
                        return;
                    }
                    /* istanbul ignore else: don't run if already running */
                    if(!scope._bUpdateSelectedStyles){
                        scope._bUpdateSelectedStyles = true;
                        scope.$apply(function(){
                            scope.updateSelectedStyles();
                        });
                    }
                };
                scope.displayElements.html.on('keydown', _keydown);
                scope.displayElements.text.on('keydown', _keydown);
                // stop updating on key up and update the display/model
                _keyup = function(){
                    scope._bUpdateSelectedStyles = false;
                };
                scope.displayElements.html.on('keyup', _keyup);
                scope.displayElements.text.on('keyup', _keyup);
                // stop updating on key up and update the display/model
                _keypress = function(event, eventData){
                    // bug fix for Firefox.  If we are selecting a <a> already, any characters will
                    // be added within the <a> which is bad!
                    /* istanbul ignore next: don't see how to test this... */
                    if (taSelection.getSelection) {
                        var _selection = taSelection.getSelection();
                        // in a weird case (can't reproduce) taSelection.getSelectionElement() can be undefined!!
                        // this comes from range.commonAncestorContainer;
                        // so I check for this here which fixes the error case
                        if (taSelection.getSelectionElement() && taSelection.getSelectionElement().nodeName.toLowerCase() === 'a') {
                            // check and see if we are at the edge of the <a>
                            if (_selection.start.element.nodeType === 3 &&
                                _selection.start.element.textContent.length === _selection.end.offset) {
                                // we are at the end of the <a>!!!
                                // so move the selection to after the <a>!!
                                taSelection.setSelectionAfterElement(taSelection.getSelectionElement());
                            }
                            if (_selection.start.element.nodeType === 3 &&
                                _selection.start.offset === 0) {
                                // we are at the start of the <a>!!!
                                // so move the selection before the <a>!!
                                taSelection.setSelectionBeforeElement(taSelection.getSelectionElement());
                            }
                        }
                    }
                    /* istanbul ignore else: this is for catching the jqLite testing*/
                    if(eventData) angular.extend(event, eventData);
                    scope.$apply(function(){
                        if(_editorFunctions.sendKeyCommand(event)){
                            /* istanbul ignore else: don't run if already running */
                            if(!scope._bUpdateSelectedStyles){
                                scope.updateSelectedStyles();
                            }
                            event.preventDefault();
                            return false;
                        }
                    });
                };
                scope.displayElements.html.on('keypress', _keypress);
                scope.displayElements.text.on('keypress', _keypress);
                // update the toolbar active states when we click somewhere in the text/html boxed
                _mouseup = function(){
                    // ensure only one execution of updateSelectedStyles()
                    scope._bUpdateSelectedStyles = false;
                    // for some reason, unless we do a $timeout here, after a _mouseup when the line is
                    // highlighted, and instead use a scope.$apply(function(){ scope.updateSelectedStyles(); });
                    // doesn't work properly, so we replaced this with:
                    /* istanbul ignore next: not tested  */
                    $timeout(function() { scope.updateSelectedStyles(); }, 0);
                };
                scope.displayElements.html.on('mouseup', _mouseup);
                scope.displayElements.text.on('mouseup', _mouseup);
            }
        };
    }
]);
textAngular.service('textAngularManager', ['taToolExecuteAction', 'taTools', 'taRegisterTool', '$interval', '$rootScope', '$log',
    function(taToolExecuteAction, taTools, taRegisterTool, $interval, $rootScope, $log){
    // this service is used to manage all textAngular editors and toolbars.
    // All publicly published functions that modify/need to access the toolbar or editor scopes should be in here
    // these contain references to all the editors and toolbars that have been initialised in this app
    var toolbars = {}, editors = {};
    // we touch the time any change occurs through register of an editor or tool so that we
    // in the future will fire and event to trigger an updateSelection
    var timeRecentModification = 0;
    var updateStyles = function(selectedElement){
        angular.forEach(editors, function(editor) {
            editor.editorFunctions.updateSelectedStyles(selectedElement);
        });
    };
    var triggerInterval = 50;
    var triggerIntervalTimer;
    var setupTriggerUpdateStyles = function() {
        timeRecentModification = Date.now();
        /* istanbul ignore next: setup a one time updateStyles() */
        triggerIntervalTimer = $interval(function() {
            updateStyles();
            triggerIntervalTimer = undefined;
        }, triggerInterval, 1); // only trigger once
    };
    /* istanbul ignore next: make sure clean up on destroy */
    $rootScope.$on('destroy', function() {
        if (triggerIntervalTimer) {
            $interval.cancel(triggerIntervalTimer);
            triggerIntervalTimer = undefined;
        }
    });
    var touchModification = function() {
        if (Math.abs(Date.now() - timeRecentModification) > triggerInterval) {
            // we have already triggered the updateStyles a long time back... so setup it again...
            setupTriggerUpdateStyles();
        }
    };
    // when we focus into a toolbar, we need to set the TOOLBAR's $parent to be the toolbars it's linked to.
    // We also need to set the tools to be updated to be the toolbars...
    return {
        // register an editor and the toolbars that it is affected by
        registerEditor: function(editorName, editorScope, targetToolbars){
            // NOTE: name === editorScope._name
            // targetToolbars is an [] of 'toolbar name's
            // targetToolbars are optional, we don't require a toolbar to function
            if(!editorName || editorName === '') throw('textAngular Error: An editor requires a name');
            if(!editorScope) throw('textAngular Error: An editor requires a scope');
            if(editors[editorName]) throw('textAngular Error: An Editor with name "' + editorName + '" already exists');
            editors[editorName] = {
                scope: editorScope,
                toolbars: targetToolbars,
                // toolbarScopes used by this editor
                toolbarScopes: [],
                _registerToolbarScope: function(toolbarScope){
                    // add to the list late
                    if(this.toolbars.indexOf(toolbarScope.name) >= 0) {
                        // if this toolbarScope is being used by this editor we add it as one of the scopes
                        this.toolbarScopes.push(toolbarScope);
                    }
                },
                // this is a suite of functions the editor should use to update all it's linked toolbars
                editorFunctions: {
                    disable: function(){
                        // disable all linked toolbars
                        angular.forEach(editors[editorName].toolbarScopes, function(toolbarScope){
                            toolbarScope.disabled = true;
                        });
                    },
                    enable: function(){
                        // enable all linked toolbars
                        angular.forEach(editors[editorName].toolbarScopes, function(toolbarScope){
                            toolbarScope.disabled = false;
                        });
                    },
                    focus: function(){
                        // this should be called when the editor is focussed
                        angular.forEach(editors[editorName].toolbarScopes, function(toolbarScope){
                            toolbarScope._parent = editorScope;
                            toolbarScope.disabled = false;
                            toolbarScope.focussed = true;
                        });
                        editorScope.focussed = true;
                    },
                    unfocus: function(){
                        // this should be called when the editor becomes unfocussed
                        angular.forEach(editors[editorName].toolbarScopes, function(toolbarScope){
                            toolbarScope.disabled = true;
                            toolbarScope.focussed = false;
                        });
                        editorScope.focussed = false;
                    },
                    updateSelectedStyles: function(selectedElement){
                        // update the active state of all buttons on liked toolbars
                        angular.forEach(editors[editorName].toolbarScopes, function(toolbarScope){
                            angular.forEach(toolbarScope.tools, function(toolScope){
                                if(toolScope.activeState){
                                    toolbarScope._parent = editorScope;
                                    // selectedElement may be undefined if nothing selected
                                    toolScope.active = toolScope.activeState(selectedElement);
                                }
                            });
                        });
                    },
                    sendKeyCommand: function(event){
                        // we return true if we applied an action, false otherwise
                        var result = false;
                        if(event.ctrlKey || event.metaKey || event.specialKey) angular.forEach(taTools, function(tool, name){
                            if(tool.commandKeyCode && (tool.commandKeyCode === event.which || tool.commandKeyCode === event.specialKey)){
                                for(var _t = 0; _t < editors[editorName].toolbarScopes.length; _t++){
                                    if(editors[editorName].toolbarScopes[_t].tools[name] !== undefined){
                                        taToolExecuteAction.call(editors[editorName].toolbarScopes[_t].tools[name], editorScope);
                                        result = true;
                                        break;
                                    }
                                }
                            }
                        });
                        return result;
                    },
                    triggerElementSelect: function(event, element){
                        // search through the taTools to see if a match for the tag is made.
                        // if there is, see if the tool is on a registered toolbar and not disabled.
                        // NOTE: This can trigger on MULTIPLE tools simultaneously.
                        var elementHasAttrs = function(_element, attrs){
                            var result = true;
                            for(var i = 0; i < attrs.length; i++) result = result && _element.attr(attrs[i]);
                            return result;
                        };
                        var workerTools = [];
                        var unfilteredTools = {};
                        var result = false;
                        element = angular.element(element);
                        // get all valid tools by element name, keep track if one matches the
                        var onlyWithAttrsFilter = false;
                        angular.forEach(taTools, function(tool, name){
                            if(
                                tool.onElementSelect &&
                                tool.onElementSelect.element &&
                                tool.onElementSelect.element.toLowerCase() === element[0].tagName.toLowerCase() &&
                                (!tool.onElementSelect.filter || tool.onElementSelect.filter(element))
                            ){
                                // this should only end up true if the element matches the only attributes
                                onlyWithAttrsFilter = onlyWithAttrsFilter ||
                                    (angular.isArray(tool.onElementSelect.onlyWithAttrs) && elementHasAttrs(element, tool.onElementSelect.onlyWithAttrs));
                                if(!tool.onElementSelect.onlyWithAttrs || elementHasAttrs(element, tool.onElementSelect.onlyWithAttrs)) unfilteredTools[name] = tool;
                            }
                        });
                        // if we matched attributes to filter on, then filter, else continue
                        if(onlyWithAttrsFilter){
                            angular.forEach(unfilteredTools, function(tool, name){
                                if(tool.onElementSelect.onlyWithAttrs && elementHasAttrs(element, tool.onElementSelect.onlyWithAttrs)) workerTools.push({'name': name, 'tool': tool});
                            });
                            // sort most specific (most attrs to find) first
                            workerTools.sort(function(a,b){
                                return b.tool.onElementSelect.onlyWithAttrs.length - a.tool.onElementSelect.onlyWithAttrs.length;
                            });
                        }else{
                            angular.forEach(unfilteredTools, function(tool, name){
                                workerTools.push({'name': name, 'tool': tool});
                            });
                        }
                        // Run the actions on the first visible filtered tool only
                        if(workerTools.length > 0){
                            for(var _i = 0; _i < workerTools.length; _i++){
                                var tool = workerTools[_i].tool;
                                var name = workerTools[_i].name;
                                for(var _t = 0; _t < editors[editorName].toolbarScopes.length; _t++){
                                    if(editors[editorName].toolbarScopes[_t].tools[name] !== undefined){
                                        tool.onElementSelect.action.call(editors[editorName].toolbarScopes[_t].tools[name], event, element, editorScope);
                                        result = true;
                                        break;
                                    }
                                }
                                if(result) break;
                            }
                        }
                        return result;
                    }
                }
            };
            angular.forEach(targetToolbars, function(_name){
                if(toolbars[_name]) {
                    editors[editorName].toolbarScopes.push(toolbars[_name]);
                }
                // if it doesn't exist it may not have been compiled yet and it will be added later
            });
            touchModification();
            return editors[editorName].editorFunctions;
        },
        // retrieve editor by name, largely used by testing suites only
        retrieveEditor: function(name){
            return editors[name];
        },
        unregisterEditor: function(name){
            delete editors[name];
            touchModification();
        },
        // registers a toolbar such that it can be linked to editors
        registerToolbar: function(toolbarScope){
            if(!toolbarScope) throw('textAngular Error: A toolbar requires a scope');
            if(!toolbarScope.name || toolbarScope.name === '') throw('textAngular Error: A toolbar requires a name');
            if(toolbars[toolbarScope.name]) throw('textAngular Error: A toolbar with name "' + toolbarScope.name + '" already exists');
            toolbars[toolbarScope.name] = toolbarScope;
            // walk all the editors and connect this toolbarScope to the editors.... if we need to.  This way, it does
            // not matter if we register the editors after the toolbars or not
            // Note the editor will ignore this toolbarScope if it is not connected to it...
            angular.forEach(editors, function(_editor){
                _editor._registerToolbarScope(toolbarScope);
            });
            touchModification();
        },
        // retrieve toolbar by name, largely used by testing suites only
        retrieveToolbar: function(name){
            return toolbars[name];
        },
        // retrieve toolbars by editor name, largely used by testing suites only
        retrieveToolbarsViaEditor: function(name){
            var result = [], _this = this;
            angular.forEach(this.retrieveEditor(name).toolbars, function(name){
                result.push(_this.retrieveToolbar(name));
            });
            return result;
        },
        unregisterToolbar: function(name){
            delete toolbars[name];
            touchModification();
        },
        // functions for updating the toolbar buttons display
        updateToolsDisplay: function(newTaTools){
            // pass a partial struct of the taTools, this allows us to update the tools on the fly, will not change the defaults.
            var _this = this;
            angular.forEach(newTaTools, function(_newTool, key){
                _this.updateToolDisplay(key, _newTool);
            });
        },
        // this function resets all toolbars to their default tool definitions
        resetToolsDisplay: function(){
            var _this = this;
            angular.forEach(taTools, function(_newTool, key){
                _this.resetToolDisplay(key);
            });
            touchModification();
        },
        // update a tool on all toolbars
        updateToolDisplay: function(toolKey, _newTool){
            var _this = this;
            angular.forEach(toolbars, function(toolbarScope, toolbarKey){
                _this.updateToolbarToolDisplay(toolbarKey, toolKey, _newTool);
            });
            touchModification();
        },
        // resets a tool to the default/starting state on all toolbars
        resetToolDisplay: function(toolKey){
            var _this = this;
            angular.forEach(toolbars, function(toolbarScope, toolbarKey){
                _this.resetToolbarToolDisplay(toolbarKey, toolKey);
            });
            touchModification();
        },
        // update a tool on a specific toolbar
        updateToolbarToolDisplay: function(toolbarKey, toolKey, _newTool){
            if(toolbars[toolbarKey]) toolbars[toolbarKey].updateToolDisplay(toolKey, _newTool);
            else throw('textAngular Error: No Toolbar with name "' + toolbarKey + '" exists');
        },
        // reset a tool on a specific toolbar to it's default starting value
        resetToolbarToolDisplay: function(toolbarKey, toolKey){
            if(toolbars[toolbarKey]) toolbars[toolbarKey].updateToolDisplay(toolKey, taTools[toolKey], true);
            else throw('textAngular Error: No Toolbar with name "' + toolbarKey + '" exists');
        },
        // removes a tool from all toolbars and it's definition
        removeTool: function(toolKey){
            delete taTools[toolKey];
            angular.forEach(toolbars, function(toolbarScope){
                delete toolbarScope.tools[toolKey];
                for(var i = 0; i < toolbarScope.toolbar.length; i++){
                    var toolbarIndex;
                    for(var j = 0; j < toolbarScope.toolbar[i].length; j++){
                        if(toolbarScope.toolbar[i][j] === toolKey){
                            toolbarIndex = {
                                group: i,
                                index: j
                            };
                            break;
                        }
                        if(toolbarIndex !== undefined) break;
                    }
                    if(toolbarIndex !== undefined){
                        toolbarScope.toolbar[toolbarIndex.group].slice(toolbarIndex.index, 1);
                        toolbarScope._$element.children().eq(toolbarIndex.group).children().eq(toolbarIndex.index).remove();
                    }
                }
            });
            touchModification();
        },
        // toolkey, toolDefinition are required. If group is not specified will pick the last group, if index isnt defined will append to group
        addTool: function(toolKey, toolDefinition, group, index){
            taRegisterTool(toolKey, toolDefinition);
            angular.forEach(toolbars, function(toolbarScope){
                toolbarScope.addTool(toolKey, toolDefinition, group, index);
            });
            touchModification();
        },
        // adds a Tool but only to one toolbar not all
        addToolToToolbar: function(toolKey, toolDefinition, toolbarKey, group, index){
            taRegisterTool(toolKey, toolDefinition);
            toolbars[toolbarKey].addTool(toolKey, toolDefinition, group, index);
            touchModification();
        },
        // this is used when externally the html of an editor has been changed and textAngular needs to be notified to update the model.
        // this will call a $digest if not already happening
        refreshEditor: function(name){
            if(editors[name]){
                editors[name].scope.updateTaBindtaTextElement();
                /* istanbul ignore else: phase catch */
                if(!editors[name].scope.$$phase) editors[name].scope.$digest();
            }else throw('textAngular Error: No Editor with name "' + name + '" exists');
            touchModification();
        },
        // this is used by taBind to send a key command in response to a special key event
        sendKeyCommand: function(scope, event){
            var _editor = editors[scope._name];
            /* istanbul ignore else: if nothing to do, do nothing */
            if (_editor && _editor.editorFunctions.sendKeyCommand(event)) {
                /* istanbul ignore else: don't run if already running */
                if(!scope._bUpdateSelectedStyles){
                    scope.updateSelectedStyles();
                }
                event.preventDefault();
                return false;
            }
        },
        //
        // When a toolbar and tools are created, it isn't until there is a key event or mouse event
        // that the updateSelectedStyles() is called behind the scenes.
        // This function forces an update through the existing editors to help the application make sure
        // the inital state is correct.
        //
        updateStyles: updateStyles,
        // return the current version of textAngular in use to the user
        getVersion: function () { return textAngularVersion; },
        // for testing
        getToolbarScopes: function () {
            var tmp=[];
            angular.forEach(editors, function (_editor) {
                tmp = tmp.concat(_editor.toolbarScopes);
            });
            return tmp;
        }
/********************** not functional yet
        // save the selection ('range') for the given editor
        saveFocusSelection: function (name, range) {
            editors[name].savedFocusRange = range;
        },
        // restore the saved selection from when the focus was lost
        restoreFocusSelection: function(name, scope) {
            // we only do this if NOT focussed and saved...
            if (editors[name].savedFocusRange && !scope.focussed) {
                try {
                    var _r = rangy.restoreRange(editors[name].savedFocusRange);
                    var _sel = rangy.getSelection();
                    _sel.addRange(_r);
                } catch(e) {}
            }
        }
*************/
    };
}]);
textAngular.directive('textAngularToolbar', [
    '$compile', 'textAngularManager', 'taOptions', 'taTools', 'taToolExecuteAction', '$window',
    function($compile, textAngularManager, taOptions, taTools, taToolExecuteAction, $window){
        return {
            scope: {
                name: '@' // a name IS required
            },
            restrict: "EA",
            link: function(scope, element, attrs){
                if(!scope.name || scope.name === '') throw('textAngular Error: A toolbar requires a name');
                angular.extend(scope, angular.copy(taOptions));
                if(attrs.taToolbar)						scope.toolbar = scope.$parent.$eval(attrs.taToolbar);
                if(attrs.taToolbarClass)				scope.classes.toolbar = attrs.taToolbarClass;
                if(attrs.taToolbarGroupClass)			scope.classes.toolbarGroup = attrs.taToolbarGroupClass;
                if(attrs.taToolbarButtonClass)			scope.classes.toolbarButton = attrs.taToolbarButtonClass;
                if(attrs.taToolbarActiveButtonClass)	scope.classes.toolbarButtonActive = attrs.taToolbarActiveButtonClass;
                if(attrs.taFocussedClass)				scope.classes.focussed = attrs.taFocussedClass;

                scope.disabled = true;
                scope.focussed = false;
                scope._$element = element;
                element[0].innerHTML = '';
                element.addClass("ta-toolbar " + scope.classes.toolbar);

                scope.$watch('focussed', function(){
                    if(scope.focussed) element.addClass(scope.classes.focussed);
                    else element.removeClass(scope.classes.focussed);
                });

                var setupToolElement = function(toolDefinition, toolScope){
                    var toolElement;
                    if(toolDefinition && toolDefinition.display){
                        toolElement = angular.element(toolDefinition.display);
                    }
                    else toolElement = angular.element("<button type='button'>");

                    if(toolDefinition && toolDefinition["class"]) toolElement.addClass(toolDefinition["class"]);
                    else toolElement.addClass(scope.classes.toolbarButton);

                    toolElement.attr('name', toolScope.name);
                    // important to not take focus from the main text/html entry
                    toolElement.attr('ta-button', 'ta-button');
                    toolElement.attr('ng-disabled', 'isDisabled()');
                    toolElement.attr('tabindex', '-1');
                    toolElement.attr('ng-click', 'executeAction()');
                    toolElement.attr('ng-class', 'displayActiveToolClass(active)');

                    if (toolDefinition && toolDefinition.tooltiptext) {
                        toolElement.attr('title', toolDefinition.tooltiptext);
                    }
                    if(toolDefinition && !toolDefinition.display && !toolScope._display){
                        // first clear out the current contents if any
                        toolElement[0].innerHTML = '';
                        // add the buttonText
                        if(toolDefinition.buttontext) toolElement[0].innerHTML = toolDefinition.buttontext;
                        // add the icon to the front of the button if there is content
                        if(toolDefinition.iconclass){
                            var icon = angular.element('<i>'), content = toolElement[0].innerHTML;
                            icon.addClass(toolDefinition.iconclass);
                            toolElement[0].innerHTML = '';
                            toolElement.append(icon);
                            if(content && content !== '') toolElement.append('&nbsp;' + content);
                        }
                    }

                    toolScope._lastToolDefinition = angular.copy(toolDefinition);

                    return $compile(toolElement)(toolScope);
                };

                // Keep a reference for updating the active states later
                scope.tools = {};
                // create the tools in the toolbar
                // default functions and values to prevent errors in testing and on init
                scope._parent = {
                    disabled: true,
                    showHtml: false,
                    queryFormatBlockState: function(){ return false; },
                    queryCommandState: function(){ return false; }
                };
                var defaultChildScope = {
                    $window: $window,
                    $editor: function(){
                        // dynamically gets the editor as it is set
                        return scope._parent;
                    },
                    isDisabled: function(){
                        // view selection button is always enabled since it doesn not depend on a selction!
                        if (this.name === 'html' && scope._parent.startAction) {
                            return false;
                        }
                        // to set your own disabled logic set a function or boolean on the tool called 'disabled'
                        return ( // this bracket is important as without it it just returns the first bracket and ignores the rest
                            // when the button's disabled function/value evaluates to true
                            (typeof this.$eval('disabled') !== 'function' && this.$eval('disabled')) || this.$eval('disabled()') ||
                            // all buttons except the HTML Switch button should be disabled in the showHtml (RAW html) mode
                            (this.name !== 'html' && this.$editor().showHtml) ||
                            // if the toolbar is disabled
                            this.$parent.disabled ||
                            // if the current editor is disabled
                            this.$editor().disabled
                        );
                    },
                    displayActiveToolClass: function(active){
                        return (active)? scope.classes.toolbarButtonActive : '';
                    },
                    executeAction: taToolExecuteAction
                };

                angular.forEach(scope.toolbar, function(group){
                    // setup the toolbar group
                    var groupElement = angular.element("<div>");
                    groupElement.addClass(scope.classes.toolbarGroup);
                    angular.forEach(group, function(tool){
                        // init and add the tools to the group
                        // a tool name (key name from taTools struct)
                        //creates a child scope of the main angularText scope and then extends the childScope with the functions of this particular tool
                        // reference to the scope and element kept
                        scope.tools[tool] = angular.extend(scope.$new(true), taTools[tool], defaultChildScope, {name: tool});
                        scope.tools[tool].$element = setupToolElement(taTools[tool], scope.tools[tool]);
                        // append the tool compiled with the childScope to the group element
                        groupElement.append(scope.tools[tool].$element);
                    });
                    // append the group to the toolbar
                    element.append(groupElement);
                });

                // update a tool
                // if a value is set to null, remove from the display
                // when forceNew is set to true it will ignore all previous settings, used to reset to taTools definition
                // to reset to defaults pass in taTools[key] as _newTool and forceNew as true, ie `updateToolDisplay(key, taTools[key], true);`
                scope.updateToolDisplay = function(key, _newTool, forceNew){
                    var toolInstance = scope.tools[key];
                    if(toolInstance){
                        // get the last toolDefinition, then override with the new definition
                        if(toolInstance._lastToolDefinition && !forceNew) _newTool = angular.extend({}, toolInstance._lastToolDefinition, _newTool);
                        if(_newTool.buttontext === null && _newTool.iconclass === null && _newTool.display === null)
                            throw('textAngular Error: Tool Definition for updating "' + key + '" does not have a valid display/iconclass/buttontext value');

                        // if tool is defined on this toolbar, update/redo the tool
                        if(_newTool.buttontext === null){
                            delete _newTool.buttontext;
                        }
                        if(_newTool.iconclass === null){
                            delete _newTool.iconclass;
                        }
                        if(_newTool.display === null){
                            delete _newTool.display;
                        }

                        var toolElement = setupToolElement(_newTool, toolInstance);
                        toolInstance.$element.replaceWith(toolElement);
                        toolInstance.$element = toolElement;
                    }
                };

                // we assume here that all values passed are valid and correct
                scope.addTool = function(key, _newTool, groupIndex, index){
                    scope.tools[key] = angular.extend(scope.$new(true), taTools[key], defaultChildScope, {name: key});
                    scope.tools[key].$element = setupToolElement(taTools[key], scope.tools[key]);
                    var group;
                    if(groupIndex === undefined) groupIndex = scope.toolbar.length - 1;
                    group = angular.element(element.children()[groupIndex]);

                    if(index === undefined){
                        group.append(scope.tools[key].$element);
                        scope.toolbar[groupIndex][scope.toolbar[groupIndex].length - 1] = key;
                    }else{
                        group.children().eq(index).after(scope.tools[key].$element);
                        scope.toolbar[groupIndex][index] = key;
                    }
                };

                textAngularManager.registerToolbar(scope);

                scope.$on('$destroy', function(){
                    textAngularManager.unregisterToolbar(scope.name);
                });
            }
        };
    }
]);
textAngular.directive('textAngularVersion', ['textAngularManager',
    function(textAngularManager) {
        var version = textAngularManager.getVersion();
        return {
            restrict: "EA",
            link: function (scope, element, attrs) {
                element.html(version);
            }
        };
    }
]);

/**
 * @license AngularJS v1.3.10
 * (c) 2010-2014 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window, angular, undefined) {'use strict';

var $sanitizeMinErr = angular.$$minErr('$sanitize');

/**
 * @ngdoc module
 * @name ngSanitize
 * @description
 *
 * # ngSanitize
 *
 * The `ngSanitize` module provides functionality to sanitize HTML.
 *
 *
 * <div doc-module-components="ngSanitize"></div>
 *
 * See {@link ngSanitize.$sanitize `$sanitize`} for usage.
 */

/*
 * HTML Parser By Misko Hevery (misko@hevery.com)
 * based on:  HTML Parser By John Resig (ejohn.org)
 * Original code by Erik Arvidsson, Mozilla Public License
 * http://erik.eae.net/simplehtmlparser/simplehtmlparser.js
 *
 * // Use like so:
 * htmlParser(htmlString, {
 *     start: function(tag, attrs, unary) {},
 *     end: function(tag) {},
 *     chars: function(text) {},
 *     comment: function(text) {}
 * });
 *
 */


/**
 * @ngdoc service
 * @name $sanitize
 * @kind function
 *
 * @description
 *   The input is sanitized by parsing the HTML into tokens. All safe tokens (from a whitelist) are
 *   then serialized back to properly escaped html string. This means that no unsafe input can make
 *   it into the returned string, however, since our parser is more strict than a typical browser
 *   parser, it's possible that some obscure input, which would be recognized as valid HTML by a
 *   browser, won't make it through the sanitizer. The input may also contain SVG markup.
 *   The whitelist is configured using the functions `aHrefSanitizationWhitelist` and
 *   `imgSrcSanitizationWhitelist` of {@link ng.$compileProvider `$compileProvider`}.
 *
 * @param {string} html HTML input.
 * @returns {string} Sanitized HTML.
 *
 * @example
   <example module="sanitizeExample" deps="angular-sanitize.js">
   <file name="index.html">
     <script>
         angular.module('sanitizeExample', ['ngSanitize'])
           .controller('ExampleController', ['$scope', '$sce', function($scope, $sce) {
             $scope.snippet =
               '<p style="color:blue">an html\n' +
               '<em onmouseover="this.textContent=\'PWN3D!\'">click here</em>\n' +
               'snippet</p>';
             $scope.deliberatelyTrustDangerousSnippet = function() {
               return $sce.trustAsHtml($scope.snippet);
             };
           }]);
     </script>
     <div ng-controller="ExampleController">
        Snippet: <textarea ng-model="snippet" cols="60" rows="3"></textarea>
       <table>
         <tr>
           <td>Directive</td>
           <td>How</td>
           <td>Source</td>
           <td>Rendered</td>
         </tr>
         <tr id="bind-html-with-sanitize">
           <td>ng-bind-html</td>
           <td>Automatically uses $sanitize</td>
           <td><pre>&lt;div ng-bind-html="snippet"&gt;<br/>&lt;/div&gt;</pre></td>
           <td><div ng-bind-html="snippet"></div></td>
         </tr>
         <tr id="bind-html-with-trust">
           <td>ng-bind-html</td>
           <td>Bypass $sanitize by explicitly trusting the dangerous value</td>
           <td>
           <pre>&lt;div ng-bind-html="deliberatelyTrustDangerousSnippet()"&gt;
&lt;/div&gt;</pre>
           </td>
           <td><div ng-bind-html="deliberatelyTrustDangerousSnippet()"></div></td>
         </tr>
         <tr id="bind-default">
           <td>ng-bind</td>
           <td>Automatically escapes</td>
           <td><pre>&lt;div ng-bind="snippet"&gt;<br/>&lt;/div&gt;</pre></td>
           <td><div ng-bind="snippet"></div></td>
         </tr>
       </table>
       </div>
   </file>
   <file name="protractor.js" type="protractor">
     it('should sanitize the html snippet by default', function() {
       expect(element(by.css('#bind-html-with-sanitize div')).getInnerHtml()).
         toBe('<p>an html\n<em>click here</em>\nsnippet</p>');
     });

     it('should inline raw snippet if bound to a trusted value', function() {
       expect(element(by.css('#bind-html-with-trust div')).getInnerHtml()).
         toBe("<p style=\"color:blue\">an html\n" +
              "<em onmouseover=\"this.textContent='PWN3D!'\">click here</em>\n" +
              "snippet</p>");
     });

     it('should escape snippet without any filter', function() {
       expect(element(by.css('#bind-default div')).getInnerHtml()).
         toBe("&lt;p style=\"color:blue\"&gt;an html\n" +
              "&lt;em onmouseover=\"this.textContent='PWN3D!'\"&gt;click here&lt;/em&gt;\n" +
              "snippet&lt;/p&gt;");
     });

     it('should update', function() {
       element(by.model('snippet')).clear();
       element(by.model('snippet')).sendKeys('new <b onclick="alert(1)">text</b>');
       expect(element(by.css('#bind-html-with-sanitize div')).getInnerHtml()).
         toBe('new <b>text</b>');
       expect(element(by.css('#bind-html-with-trust div')).getInnerHtml()).toBe(
         'new <b onclick="alert(1)">text</b>');
       expect(element(by.css('#bind-default div')).getInnerHtml()).toBe(
         "new &lt;b onclick=\"alert(1)\"&gt;text&lt;/b&gt;");
     });
   </file>
   </example>
 */
function $SanitizeProvider() {
  this.$get = ['$$sanitizeUri', function($$sanitizeUri) {
    return function(html) {
      if (typeof arguments[1] != 'undefined') {
        arguments[1].version = 'taSanitize';
      }
      var buf = [];
      htmlParser(html, htmlSanitizeWriter(buf, function(uri, isImage) {
        return !/^unsafe/.test($$sanitizeUri(uri, isImage));
      }));
      return buf.join('');
    };
  }];
}

function sanitizeText(chars) {
  var buf = [];
  var writer = htmlSanitizeWriter(buf, angular.noop);
  writer.chars(chars);
  return buf.join('');
}


// Regular Expressions for parsing tags and attributes
var START_TAG_REGEXP =
       /^<((?:[a-zA-Z])[\w:-]*)((?:\s+[\w:-]+(?:\s*=\s*(?:(?:"[^"]*")|(?:'[^']*')|[^>\s]+))?)*)\s*(\/?)\s*(>?)/,
  END_TAG_REGEXP = /^<\/\s*([\w:-]+)[^>]*>/,
  ATTR_REGEXP = /([\w:-]+)(?:\s*=\s*(?:(?:"((?:[^"])*)")|(?:'((?:[^'])*)')|([^>\s]+)))?/g,
  BEGIN_TAG_REGEXP = /^</,
  BEGING_END_TAGE_REGEXP = /^<\//,
  COMMENT_REGEXP = /<!--(.*?)-->/g,
  SINGLE_COMMENT_REGEXP = /(^<!--.*?-->)/,
  DOCTYPE_REGEXP = /<!DOCTYPE([^>]*?)>/i,
  CDATA_REGEXP = /<!\[CDATA\[(.*?)]]>/g,
  SURROGATE_PAIR_REGEXP = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g,
  // Match everything outside of normal chars and " (quote character)
  NON_ALPHANUMERIC_REGEXP = /([^\#-~| |!])/g,
  WHITE_SPACE_REGEXP = /^(\s+)/;


// Good source of info about elements and attributes
// http://dev.w3.org/html5/spec/Overview.html#semantics
// http://simon.html5.org/html-elements

// Safe Void Elements - HTML5
// http://dev.w3.org/html5/spec/Overview.html#void-elements
var voidElements = makeMap("area,br,col,hr,img,wbr,input");

// Elements that you can, intentionally, leave open (and which close themselves)
// http://dev.w3.org/html5/spec/Overview.html#optional-tags
var optionalEndTagBlockElements = makeMap("colgroup,dd,dt,li,p,tbody,td,tfoot,th,thead,tr"),
    optionalEndTagInlineElements = makeMap("rp,rt"),
    optionalEndTagElements = angular.extend({},
                                            optionalEndTagInlineElements,
                                            optionalEndTagBlockElements);

// Safe Block Elements - HTML5
var blockElements = angular.extend({}, optionalEndTagBlockElements, makeMap("address,article," +
        "aside,blockquote,caption,center,del,dir,div,dl,figure,figcaption,footer,h1,h2,h3,h4,h5," +
        "h6,header,hgroup,hr,ins,map,menu,nav,ol,pre,script,section,table,ul"));

// Inline Elements - HTML5
var inlineElements = angular.extend({}, optionalEndTagInlineElements, makeMap("a,abbr,acronym,b," +
        "bdi,bdo,big,br,cite,code,del,dfn,em,font,i,img,ins,kbd,label,map,mark,q,ruby,rp,rt,s," +
        "samp,small,span,strike,strong,sub,sup,time,tt,u,var"));

// SVG Elements
// https://wiki.whatwg.org/wiki/Sanitization_rules#svg_Elements
var svgElements = makeMap("animate,animateColor,animateMotion,animateTransform,circle,defs," +
        "desc,ellipse,font-face,font-face-name,font-face-src,g,glyph,hkern,image,linearGradient," +
        "line,marker,metadata,missing-glyph,mpath,path,polygon,polyline,radialGradient,rect,set," +
        "stop,svg,switch,text,title,tspan,use");

// Special Elements (can contain anything)
var specialElements = makeMap("script,style");

var validElements = angular.extend({},
                                   voidElements,
                                   blockElements,
                                   inlineElements,
                                   optionalEndTagElements,
                                   svgElements);

//Attributes that have href and hence need to be sanitized
var uriAttrs = makeMap("background,cite,href,longdesc,src,usemap,xlink:href");

var htmlAttrs = makeMap('abbr,align,alt,axis,bgcolor,border,cellpadding,cellspacing,class,clear,'+
    'color,cols,colspan,compact,coords,dir,face,headers,height,hreflang,hspace,'+
    'id,ismap,lang,language,nohref,nowrap,rel,rev,rows,rowspan,rules,'+
    'scope,scrolling,shape,size,span,start,summary,target,title,type,'+
    'valign,value,vspace,width');

// SVG attributes (without "id" and "name" attributes)
// https://wiki.whatwg.org/wiki/Sanitization_rules#svg_Attributes
var svgAttrs = makeMap('accent-height,accumulate,additive,alphabetic,arabic-form,ascent,' +
    'attributeName,attributeType,baseProfile,bbox,begin,by,calcMode,cap-height,class,color,' +
    'color-rendering,content,cx,cy,d,dx,dy,descent,display,dur,end,fill,fill-rule,font-family,' +
    'font-size,font-stretch,font-style,font-variant,font-weight,from,fx,fy,g1,g2,glyph-name,' +
    'gradientUnits,hanging,height,horiz-adv-x,horiz-origin-x,ideographic,k,keyPoints,' +
    'keySplines,keyTimes,lang,marker-end,marker-mid,marker-start,markerHeight,markerUnits,' +
    'markerWidth,mathematical,max,min,offset,opacity,orient,origin,overline-position,' +
    'overline-thickness,panose-1,path,pathLength,points,preserveAspectRatio,r,refX,refY,' +
    'repeatCount,repeatDur,requiredExtensions,requiredFeatures,restart,rotate,rx,ry,slope,stemh,' +
    'stemv,stop-color,stop-opacity,strikethrough-position,strikethrough-thickness,stroke,' +
    'stroke-dasharray,stroke-dashoffset,stroke-linecap,stroke-linejoin,stroke-miterlimit,' +
    'stroke-opacity,stroke-width,systemLanguage,target,text-anchor,to,transform,type,u1,u2,' +
    'underline-position,underline-thickness,unicode,unicode-range,units-per-em,values,version,' +
    'viewBox,visibility,width,widths,x,x-height,x1,x2,xlink:actuate,xlink:arcrole,xlink:role,' +
    'xlink:show,xlink:title,xlink:type,xml:base,xml:lang,xml:space,xmlns,xmlns:xlink,y,y1,y2,' +
    'zoomAndPan');

var validAttrs = angular.extend({},
                                uriAttrs,
                                svgAttrs,
                                htmlAttrs);

function makeMap(str) {
  var obj = {}, items = str.split(','), i;
  for (i = 0; i < items.length; i++) obj[items[i]] = true;
  return obj;
}


/**
 * @example
 * htmlParser(htmlString, {
 *     start: function(tag, attrs, unary) {},
 *     end: function(tag) {},
 *     chars: function(text) {},
 *     comment: function(text) {}
 * });
 *
 * @param {string} html string
 * @param {object} handler
 */
function htmlParser(html, handler) {
  if (typeof html !== 'string') {
    if (html === null || typeof html === 'undefined') {
      html = '';
    } else {
      html = '' + html;
    }
  }
  var index, chars, match, stack = [], last = html, text;
  stack.last = function() { return stack[ stack.length - 1 ]; };

  while (html) {
    text = '';
    chars = true;

    // Make sure we're not in a script or style element
    if (!stack.last() || !specialElements[ stack.last() ]) {

      // White space
      if (WHITE_SPACE_REGEXP.test(html)) {
        match = html.match(WHITE_SPACE_REGEXP);

        if (match) {
          var mat = match[0];
          if (handler.whitespace) handler.whitespace(match[0]);
          html = html.replace(match[0], '');
          chars = false;
        }
      //Comment
      } else if (SINGLE_COMMENT_REGEXP.test(html)) {
        match = html.match(SINGLE_COMMENT_REGEXP);

        if (match) {
          if (handler.comment) handler.comment(match[1]);
          html = html.replace(match[0], '');
          chars = false;
        }
      // DOCTYPE
      } else if (DOCTYPE_REGEXP.test(html)) {
        match = html.match(DOCTYPE_REGEXP);

        if (match) {
          html = html.replace(match[0], '');
          chars = false;
        }
      // end tag
      } else if (BEGING_END_TAGE_REGEXP.test(html)) {
        match = html.match(END_TAG_REGEXP);

        if (match) {
          html = html.substring(match[0].length);
          match[0].replace(END_TAG_REGEXP, parseEndTag);
          chars = false;
        }

      // start tag
      } else if (BEGIN_TAG_REGEXP.test(html)) {
        match = html.match(START_TAG_REGEXP);

        if (match) {
          // We only have a valid start-tag if there is a '>'.
          if (match[4]) {
            html = html.substring(match[0].length);
            match[0].replace(START_TAG_REGEXP, parseStartTag);
          }
          chars = false;
        } else {
          // no ending tag found --- this piece should be encoded as an entity.
          text += '<';
          html = html.substring(1);
        }
      }

      if (chars) {
        index = html.indexOf("<");

        text += index < 0 ? html : html.substring(0, index);
        html = index < 0 ? "" : html.substring(index);

        if (handler.chars) handler.chars(decodeEntities(text));
      }

    } else {
      html = html.replace(new RegExp("([^]*)<\\s*\\/\\s*" + stack.last() + "[^>]*>", 'i'),
        function(all, text) {
          text = text.replace(COMMENT_REGEXP, "$1").replace(CDATA_REGEXP, "$1");

          if (handler.chars) handler.chars(decodeEntities(text));

          return "";
      });

      parseEndTag("", stack.last());
    }

    if (html == last) {
      throw $sanitizeMinErr('badparse', "The sanitizer was unable to parse the following block " +
                                        "of html: {0}", html);
    }
    last = html;
  }

  // Clean up any remaining tags
  parseEndTag();

  function parseStartTag(tag, tagName, rest, unary) {
    tagName = angular.lowercase(tagName);
    if (blockElements[ tagName ]) {
      while (stack.last() && inlineElements[ stack.last() ]) {
        parseEndTag("", stack.last());
      }
    }

    if (optionalEndTagElements[ tagName ] && stack.last() == tagName) {
      parseEndTag("", tagName);
    }

    unary = voidElements[ tagName ] || !!unary;

    if (!unary)
      stack.push(tagName);

    var attrs = {};

    rest.replace(ATTR_REGEXP,
      function(match, name, doubleQuotedValue, singleQuotedValue, unquotedValue) {
        var value = doubleQuotedValue
          || singleQuotedValue
          || unquotedValue
          || '';

        attrs[name] = decodeEntities(value);
    });
    if (handler.start) handler.start(tagName, attrs, unary);
  }

  function parseEndTag(tag, tagName) {
    var pos = 0, i;
    tagName = angular.lowercase(tagName);
    if (tagName)
      // Find the closest opened tag of the same type
      for (pos = stack.length - 1; pos >= 0; pos--)
        if (stack[ pos ] == tagName)
          break;

    if (pos >= 0) {
      // Close all the open elements, up the stack
      for (i = stack.length - 1; i >= pos; i--)
        if (handler.end) handler.end(stack[ i ]);

      // Remove the open elements from the stack
      stack.length = pos;
    }
  }
}

var hiddenPre=document.createElement("pre");
var spaceRe = /^(\s*)([\s\S]*?)(\s*)$/;
/**
 * decodes all entities into regular string
 * @param value
 * @returns {string} A string with decoded entities.
 */
function decodeEntities(value) {
  if (!value) { return ''; }

  // Note: IE8 does not preserve spaces at the start/end of innerHTML
  // so we must capture them and reattach them afterward
  var parts = spaceRe.exec(value);
  var spaceBefore = parts[1];
  var spaceAfter = parts[3];
  var content = parts[2];
  if (content) {
    hiddenPre.innerHTML=content.replace(/</g,"&lt;");
    // innerText depends on styling as it doesn't display hidden elements.
    // Therefore, it's better to use textContent not to cause unnecessary
    // reflows. However, IE<9 don't support textContent so the innerText
    // fallback is necessary.
    content = 'textContent' in hiddenPre ?
      hiddenPre.textContent : hiddenPre.innerText;
  }
  return spaceBefore + content + spaceAfter;
}

/**
 * Escapes all potentially dangerous characters, so that the
 * resulting string can be safely inserted into attribute or
 * element text.
 * @param value
 * @returns {string} escaped text
 */
function encodeEntities(value) {
  return value.
    replace(/&/g, '&amp;').
    replace(SURROGATE_PAIR_REGEXP, function(value) {
      var hi = value.charCodeAt(0);
      var low = value.charCodeAt(1);
      return '&#' + (((hi - 0xD800) * 0x400) + (low - 0xDC00) + 0x10000) + ';';
    }).
    replace(NON_ALPHANUMERIC_REGEXP, function(value) {
      // unsafe chars are: \u0000-\u001f \u007f-\u009f \u00ad \u0600-\u0604 \u070f \u17b4 \u17b5 \u200c-\u200f \u2028-\u202f \u2060-\u206f \ufeff \ufff0-\uffff from jslint.com/lint.html
      // decimal values are: 0-31, 127-159, 173, 1536-1540, 1807, 6068, 6069, 8204-8207, 8232-8239, 8288-8303, 65279, 65520-65535
      var c = value.charCodeAt(0);
      // if unsafe character encode
      if(c <= 159 ||
        c == 173 ||
        (c >= 1536 && c <= 1540) ||
        c == 1807 ||
        c == 6068 ||
        c == 6069 ||
        (c >= 8204 && c <= 8207) ||
        (c >= 8232 && c <= 8239) ||
        (c >= 8288 && c <= 8303) ||
        c == 65279 ||
        (c >= 65520 && c <= 65535)) return '&#' + c + ';';
      return value; // avoids multilingual issues
    }).
    replace(/</g, '&lt;').
    replace(/>/g, '&gt;');
}

var trim = (function() {
  // native trim is way faster: http://jsperf.com/angular-trim-test
  // but IE doesn't have it... :-(
  // TODO: we should move this into IE/ES5 polyfill
  if (!String.prototype.trim) {
    return function(value) {
      return angular.isString(value) ? value.replace(/^\s\s*/, '').replace(/\s\s*$/, '') : value;
    };
  }
  return function(value) {
    return angular.isString(value) ? value.trim() : value;
  };
})();

// Custom logic for accepting certain style options only - textAngular
// Currently allows only the color, background-color, text-align, float, width and height attributes
// all other attributes should be easily done through classes.
function validStyles(styleAttr){
	var result = '';
	var styleArray = styleAttr.split(';');
	angular.forEach(styleArray, function(value){
		var v = value.split(':');
		if(v.length == 2){
			var key = trim(angular.lowercase(v[0]));
			var value = trim(angular.lowercase(v[1]));
			if(
				(key === 'color' || key === 'background-color') && (
					value.match(/^rgb\([0-9%,\. ]*\)$/i)
					|| value.match(/^rgba\([0-9%,\. ]*\)$/i)
					|| value.match(/^hsl\([0-9%,\. ]*\)$/i)
					|| value.match(/^hsla\([0-9%,\. ]*\)$/i)
					|| value.match(/^#[0-9a-f]{3,6}$/i)
					|| value.match(/^[a-z]*$/i)
				)
			||
				key === 'text-align' && (
					value === 'left'
					|| value === 'right'
					|| value === 'center'
					|| value === 'justify'
				)
			||
        key === 'text-decoration' && (
            value === 'underline'
            || value === 'line-through'
        )
      || 
        key === 'font-weight' && (
            value === 'bold'
        )
      ||
        key === 'font-style' && (
          value === 'italic'
        )
      ||
        key === 'float' && (
            value === 'left'
            || value === 'right'
            || value === 'none'
        )
      ||
        key === 'vertical-align' && (
            value === 'baseline'
            || value === 'sub'
            || value === 'super'
            || value === 'test-top'
            || value === 'text-bottom'
            || value === 'middle'
            || value === 'top'
            || value === 'bottom'
            || value.match(/[0-9]*(px|em)/)
            || value.match(/[0-9]+?%/)
        )
      ||
        key === 'font-size' && (
            value === 'xx-small'
            || value === 'x-small'
            || value === 'small'
            || value === 'medium'
            || value === 'large'
            || value === 'x-large'
            || value === 'xx-large'
            || value === 'larger'
            || value === 'smaller'
            || value.match(/[0-9]*\.?[0-9]*(px|em|rem|mm|q|cm|in|pt|pc|%)/)
                               )
			||
				(key === 'width' || key === 'height') && (
					value.match(/[0-9\.]*(px|em|rem|%)/)
				)
			|| // Reference #520
				(key === 'direction' && value.match(/^ltr|rtl|initial|inherit$/))
			) result += key + ': ' + value + ';';
		}
	});
	return result;
}

// this function is used to manually allow specific attributes on specific tags with certain prerequisites
function validCustomTag(tag, attrs, lkey, value){
	// catch the div placeholder for the iframe replacement
    if (tag === 'img' && attrs['ta-insert-video']){
        if(lkey === 'ta-insert-video' || lkey === 'allowfullscreen' || lkey === 'frameborder' || (lkey === 'contenteditable' && value === 'false')) return true;
    }
    return false;
}

/**
 * create an HTML/XML writer which writes to buffer
 * @param {Array} buf use buf.jain('') to get out sanitized html string
 * @returns {object} in the form of {
 *     start: function(tag, attrs, unary) {},
 *     end: function(tag) {},
 *     chars: function(text) {},
 *     comment: function(text) {}
 * }
 */
function htmlSanitizeWriter(buf, uriValidator) {
  var ignore = false;
  var out = angular.bind(buf, buf.push);
  return {
    start: function(tag, attrs, unary) {
      tag = angular.lowercase(tag);
      if (!ignore && specialElements[tag]) {
        ignore = tag;
      }
      if (!ignore && validElements[tag] === true) {
        out('<');
        out(tag);
        angular.forEach(attrs, function(value, key) {
          var lkey=angular.lowercase(key);
          var isImage=(tag === 'img' && lkey === 'src') || (lkey === 'background');
          if ((lkey === 'style' && (value = validStyles(value)) !== '') || validCustomTag(tag, attrs, lkey, value) || validAttrs[lkey] === true &&
            (uriAttrs[lkey] !== true || uriValidator(value, isImage))) {
            out(' ');
            out(key);
            out('="');
            out(encodeEntities(value));
            out('"');
          }
        });
        out(unary ? '/>' : '>');
      }
    },
    comment: function (com) {
      out(com);
    },
    whitespace: function (ws) {
      out(encodeEntities(ws));
    },
    end: function(tag) {
        tag = angular.lowercase(tag);
        if (!ignore && validElements[tag] === true) {
          out('</');
          out(tag);
          out('>');
        }
        if (tag == ignore) {
          ignore = false;
        }
      },
    chars: function(chars) {
        if (!ignore) {
          out(encodeEntities(chars));
        }
      }
  };
}


// define ngSanitize module and register $sanitize service
angular.module('ngSanitize', []).provider('$sanitize', $SanitizeProvider);

/* global sanitizeText: false */

/**
 * @ngdoc filter
 * @name linky
 * @kind function
 *
 * @description
 * Finds links in text input and turns them into html links. Supports http/https/ftp/mailto and
 * plain email address links.
 *
 * Requires the {@link ngSanitize `ngSanitize`} module to be installed.
 *
 * @param {string} text Input text.
 * @param {string} target Window (_blank|_self|_parent|_top) or named frame to open links in.
 * @returns {string} Html-linkified text.
 *
 * @usage
   <span ng-bind-html="linky_expression | linky"></span>
 *
 * @example
   <example module="linkyExample" deps="angular-sanitize.js">
     <file name="index.html">
       <script>
         angular.module('linkyExample', ['ngSanitize'])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.snippet =
               'Pretty text with some links:\n'+
               'http://angularjs.org/,\n'+
               'mailto:us@somewhere.org,\n'+
               'another@somewhere.org,\n'+
               'and one more: ftp://127.0.0.1/.';
             $scope.snippetWithTarget = 'http://angularjs.org/';
           }]);
       </script>
       <div ng-controller="ExampleController">
       Snippet: <textarea ng-model="snippet" cols="60" rows="3"></textarea>
       <table>
         <tr>
           <td>Filter</td>
           <td>Source</td>
           <td>Rendered</td>
         </tr>
         <tr id="linky-filter">
           <td>linky filter</td>
           <td>
             <pre>&lt;div ng-bind-html="snippet | linky"&gt;<br>&lt;/div&gt;</pre>
           </td>
           <td>
             <div ng-bind-html="snippet | linky"></div>
           </td>
         </tr>
         <tr id="linky-target">
          <td>linky target</td>
          <td>
            <pre>&lt;div ng-bind-html="snippetWithTarget | linky:'_blank'"&gt;<br>&lt;/div&gt;</pre>
          </td>
          <td>
            <div ng-bind-html="snippetWithTarget | linky:'_blank'"></div>
          </td>
         </tr>
         <tr id="escaped-html">
           <td>no filter</td>
           <td><pre>&lt;div ng-bind="snippet"&gt;<br>&lt;/div&gt;</pre></td>
           <td><div ng-bind="snippet"></div></td>
         </tr>
       </table>
     </file>
     <file name="protractor.js" type="protractor">
       it('should linkify the snippet with urls', function() {
         expect(element(by.id('linky-filter')).element(by.binding('snippet | linky')).getText()).
             toBe('Pretty text with some links: http://angularjs.org/, us@somewhere.org, ' +
                  'another@somewhere.org, and one more: ftp://127.0.0.1/.');
         expect(element.all(by.css('#linky-filter a')).count()).toEqual(4);
       });

       it('should not linkify snippet without the linky filter', function() {
         expect(element(by.id('escaped-html')).element(by.binding('snippet')).getText()).
             toBe('Pretty text with some links: http://angularjs.org/, mailto:us@somewhere.org, ' +
                  'another@somewhere.org, and one more: ftp://127.0.0.1/.');
         expect(element.all(by.css('#escaped-html a')).count()).toEqual(0);
       });

       it('should update', function() {
         element(by.model('snippet')).clear();
         element(by.model('snippet')).sendKeys('new http://link.');
         expect(element(by.id('linky-filter')).element(by.binding('snippet | linky')).getText()).
             toBe('new http://link.');
         expect(element.all(by.css('#linky-filter a')).count()).toEqual(1);
         expect(element(by.id('escaped-html')).element(by.binding('snippet')).getText())
             .toBe('new http://link.');
       });

       it('should work with the target property', function() {
        expect(element(by.id('linky-target')).
            element(by.binding("snippetWithTarget | linky:'_blank'")).getText()).
            toBe('http://angularjs.org/');
        expect(element(by.css('#linky-target a')).getAttribute('target')).toEqual('_blank');
       });
     </file>
   </example>
 */
angular.module('ngSanitize').filter('linky', ['$sanitize', function($sanitize) {
  var LINKY_URL_REGEXP =
        /((ftp|https?):\/\/|(www\.)|(mailto:)?[A-Za-z0-9._%+-]+@)\S*[^\s.;,(){}<>"”’]/,
      MAILTO_REGEXP = /^mailto:/;

  return function(text, target) {
    if (!text) return text;
    var match;
    var raw = text;
    var html = [];
    var url;
    var i;
    while ((match = raw.match(LINKY_URL_REGEXP))) {
      // We can not end in these as they are sometimes found at the end of the sentence
      url = match[0];
      // if we did not match ftp/http/www/mailto then assume mailto
      if (!match[2] && !match[4]) {
        url = (match[3] ? 'http://' : 'mailto:') + url;
      }
      i = match.index;
      addText(raw.substr(0, i));
      addLink(url, match[0].replace(MAILTO_REGEXP, ''));
      raw = raw.substring(i + match[0].length);
    }
    addText(raw);
    return $sanitize(html.join(''));

    function addText(text) {
      if (!text) {
        return;
      }
      html.push(sanitizeText(text));
    }

    function addLink(url, text) {
      html.push('<a ');
      if (angular.isDefined(target)) {
        html.push('target="',
                  target,
                  '" ');
      }
      html.push('href="',
                url.replace(/"/g, '&quot;'),
                '">');
      addText(text);
      html.push('</a>');
    }
  };
}]);


})(window, window.angular);


// tests against the current jqLite/jquery implementation if this can be an element
function validElementString(string){
    try{
        return angular.element(string).length !== 0;
    }catch(any){
        return false;
    }
}
// setup the global contstant functions for setting up the toolbar

// all tool definitions
var taTools = {};
/*
    A tool definition is an object with the following key/value parameters:
        action: [function(deferred, restoreSelection)]
                a function that is executed on clicking on the button - this will allways be executed using ng-click and will
                overwrite any ng-click value in the display attribute.
                The function is passed a deferred object ($q.defer()), if this is wanted to be used `return false;` from the action and
                manually call `deferred.resolve();` elsewhere to notify the editor that the action has finished.
                restoreSelection is only defined if the rangy library is included and it can be called as `restoreSelection()` to restore the users
                selection in the WYSIWYG editor.
        display: [string]?
                Optional, an HTML element to be displayed as the button. The `scope` of the button is the tool definition object with some additional functions
                If set this will cause buttontext and iconclass to be ignored
        class: [string]?
                Optional, if set will override the taOptions.classes.toolbarButton class.
        buttontext: [string]?
                if this is defined it will replace the contents of the element contained in the `display` element
        iconclass: [string]?
                if this is defined an icon (<i>) will be appended to the `display` element with this string as it's class
        tooltiptext: [string]?
                Optional, a plain text description of the action, used for the title attribute of the action button in the toolbar by default.
        activestate: [function(commonElement)]?
                this function is called on every caret movement, if it returns true then the class taOptions.classes.toolbarButtonActive
                will be applied to the `display` element, else the class will be removed
        disabled: [function()]?
                if this function returns true then the tool will have the class taOptions.classes.disabled applied to it, else it will be removed
    Other functions available on the scope are:
        name: [string]
                the name of the tool, this is the first parameter passed into taRegisterTool
        isDisabled: [function()]
                returns true if the tool is disabled, false if it isn't
        displayActiveToolClass: [function(boolean)]
                returns true if the tool is 'active' in the currently focussed toolbar
        onElementSelect: [Object]
                This object contains the following key/value pairs and is used to trigger the ta-element-select event
                element: [String]
                    an element name, will only trigger the onElementSelect action if the tagName of the element matches this string
                filter: [function(element)]?
                    an optional filter that returns a boolean, if true it will trigger the onElementSelect.
                action: [function(event, element, editorScope)]
                    the action that should be executed if the onElementSelect function runs
*/
// name and toolDefinition to add into the tools available to be added on the toolbar
function registerTextAngularTool(name, toolDefinition){
    if(!name || name === '' || taTools.hasOwnProperty(name)) throw('textAngular Error: A unique name is required for a Tool Definition');
    if(
        (toolDefinition.display && (toolDefinition.display === '' || !validElementString(toolDefinition.display))) ||
        (!toolDefinition.display && !toolDefinition.buttontext && !toolDefinition.iconclass)
    )
        throw('textAngular Error: Tool Definition for "' + name + '" does not have a valid display/iconclass/buttontext value');
    taTools[name] = toolDefinition;
}

angular.module('textAngularSetup', [])
.constant('taRegisterTool', registerTextAngularTool)
.value('taTools', taTools)
// Here we set up the global display defaults, to set your own use a angular $provider#decorator.
.value('taOptions',  {
    //////////////////////////////////////////////////////////////////////////////////////
    // forceTextAngularSanitize
    // set false to allow the textAngular-sanitize provider to be replaced
    // with angular-sanitize or a custom provider.
    forceTextAngularSanitize: true,
    ///////////////////////////////////////////////////////////////////////////////////////
    // keyMappings
    // allow customizable keyMappings for specialized key boards or languages
    //
    // keyMappings provides key mappings that are attached to a given commandKeyCode.
    // To modify a specific keyboard binding, simply provide function which returns true
    // for the event you wish to map to.
    // Or to disable a specific keyboard binding, provide a function which returns false.
    // Note: 'RedoKey' and 'UndoKey' are internally bound to the redo and undo functionality.
    // At present, the following commandKeyCodes are in use:
    // 98, 'TabKey', 'ShiftTabKey', 105, 117, 'UndoKey', 'RedoKey'
    //
    // To map to an new commandKeyCode, add a new key mapping such as:
    // {commandKeyCode: 'CustomKey', testForKey: function (event) {
    //  if (event.keyCode=57 && event.ctrlKey && !event.shiftKey && !event.altKey) return true;
    // } }
    // to the keyMappings. This example maps ctrl+9 to 'CustomKey'
    // Then where taRegisterTool(...) is called, add a commandKeyCode: 'CustomKey' and your
    // tool will be bound to ctrl+9.
    //
    // To disble one of the already bound commandKeyCodes such as 'RedoKey' or 'UndoKey' add:
    // {commandKeyCode: 'RedoKey', testForKey: function (event) { return false; } },
    // {commandKeyCode: 'UndoKey', testForKey: function (event) { return false; } },
    // to disable them.
    //
    keyMappings : [],
    toolbar: [
        ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'pre', 'quote'],
        ['bold', 'italics', 'underline', 'strikeThrough', 'ul', 'ol', 'redo', 'undo', 'clear'],
        ['justifyLeft','justifyCenter','justifyRight','justifyFull','indent','outdent'],
        ['html', 'insertImage', 'insertLink', 'insertVideo', 'wordcount', 'charcount']
    ],
    classes: {
        focussed: "focussed",
        toolbar: "btn-toolbar",
        toolbarGroup: "btn-group",
        toolbarButton: "btn btn-default",
        toolbarButtonActive: "active",
        disabled: "disabled",
        textEditor: 'form-control',
        htmlEditor: 'form-control'
    },
    defaultTagAttributes : {
        a: {target:""}
    },
    setup: {
        // wysiwyg mode
        textEditorSetup: function($element){ /* Do some processing here */ },
        // raw html
        htmlEditorSetup: function($element){ /* Do some processing here */ }
    },
    defaultFileDropHandler:
        /* istanbul ignore next: untestable image processing */
        function(file, insertAction){
            var reader = new FileReader();
            if(file.type.substring(0, 5) === 'image'){
                reader.onload = function() {
                    if(reader.result !== '') insertAction('insertImage', reader.result, true);
                };

                reader.readAsDataURL(file);
                // NOTE: For async procedures return a promise and resolve it when the editor should update the model.
                return true;
            }
            return false;
        }
})

// This is the element selector string that is used to catch click events within a taBind, prevents the default and $emits a 'ta-element-select' event
// these are individually used in an angular.element().find() call. What can go here depends on whether you have full jQuery loaded or just jQLite with angularjs.
// div is only used as div.ta-insert-video caught in filter.
.value('taSelectableElements', ['a','img'])

// This is an array of objects with the following options:
//				selector: <string> a jqLite or jQuery selector string
//				customAttribute: <string> an attribute to search for
//				renderLogic: <function(element)>
// Both or one of selector and customAttribute must be defined.
.value('taCustomRenderers', [
    {
        // Parse back out: '<div class="ta-insert-video" ta-insert-video src="' + urlLink + '" allowfullscreen="true" width="300" frameborder="0" height="250"></div>'
        // To correct video element. For now only support youtube
        selector: 'img',
        customAttribute: 'ta-insert-video',
        renderLogic: function(element){
            var iframe = angular.element('<iframe></iframe>');
            var attributes = element.prop("attributes");
            // loop through element attributes and apply them on iframe
            angular.forEach(attributes, function(attr) {
                iframe.attr(attr.name, attr.value);
            });
            iframe.attr('src', iframe.attr('ta-insert-video'));
            element.replaceWith(iframe);
        }
    }
])

.value('taTranslations', {
    // moved to sub-elements
    //toggleHTML: "Toggle HTML",
    //insertImage: "Please enter a image URL to insert",
    //insertLink: "Please enter a URL to insert",
    //insertVideo: "Please enter a youtube URL to embed",
    html: {
        tooltip: 'Toggle html / Rich Text'
    },
    // tooltip for heading - might be worth splitting
    heading: {
        tooltip: 'Heading '
    },
    p: {
        tooltip: 'Paragraph'
    },
    pre: {
        tooltip: 'Preformatted text'
    },
    ul: {
        tooltip: 'Unordered List'
    },
    ol: {
        tooltip: 'Ordered List'
    },
    quote: {
        tooltip: 'Quote/unquote selection or paragraph'
    },
    undo: {
        tooltip: 'Undo'
    },
    redo: {
        tooltip: 'Redo'
    },
    bold: {
        tooltip: 'Bold'
    },
    italic: {
        tooltip: 'Italic'
    },
    underline: {
        tooltip: 'Underline'
    },
    strikeThrough:{
        tooltip: 'Strikethrough'
    },
    justifyLeft: {
        tooltip: 'Align text left'
    },
    justifyRight: {
        tooltip: 'Align text right'
    },
    justifyFull: {
        tooltip: 'Justify text'
    },
    justifyCenter: {
        tooltip: 'Center'
    },
    indent: {
        tooltip: 'Increase indent'
    },
    outdent: {
        tooltip: 'Decrease indent'
    },
    clear: {
        tooltip: 'Clear formatting'
    },
    insertImage: {
        dialogPrompt: 'Please enter an image URL to insert',
        tooltip: 'Insert image',
        hotkey: 'the - possibly language dependent hotkey ... for some future implementation'
    },
    insertVideo: {
        tooltip: 'Insert video',
        dialogPrompt: 'Please enter a youtube URL to embed'
    },
    insertLink: {
        tooltip: 'Insert / edit link',
        dialogPrompt: "Please enter a URL to insert"
    },
    editLink: {
        reLinkButton: {
            tooltip: "Relink"
        },
        unLinkButton: {
            tooltip: "Unlink"
        },
        targetToggle: {
            buttontext: "Open in New Window"
        }
    },
    wordcount: {
        tooltip: 'Display words Count'
    },
        charcount: {
        tooltip: 'Display characters Count'
    }
})
.factory('taToolFunctions', ['$window','taTranslations', function($window, taTranslations) {
    return {
        imgOnSelectAction: function(event, $element, editorScope){
            // setup the editor toolbar
            // Credit to the work at http://hackerwins.github.io/summernote/ for this editbar logic/display
            var finishEdit = function(){
                editorScope.updateTaBindtaTextElement();
                editorScope.hidePopover();
            };
            event.preventDefault();
            editorScope.displayElements.popover.css('width', '375px');
            var container = editorScope.displayElements.popoverContainer;
            container.empty();
            var buttonGroup = angular.element('<div class="btn-group" style="padding-right: 6px;">');
            var fullButton = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" unselectable="on" tabindex="-1">100% </button>');
            fullButton.on('click', function(event){
                event.preventDefault();
                $element.css({
                    'width': '100%',
                    'height': ''
                });
                finishEdit();
            });
            var halfButton = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" unselectable="on" tabindex="-1">50% </button>');
            halfButton.on('click', function(event){
                event.preventDefault();
                $element.css({
                    'width': '50%',
                    'height': ''
                });
                finishEdit();
            });
            var quartButton = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" unselectable="on" tabindex="-1">25% </button>');
            quartButton.on('click', function(event){
                event.preventDefault();
                $element.css({
                    'width': '25%',
                    'height': ''
                });
                finishEdit();
            });
            var resetButton = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" unselectable="on" tabindex="-1">Reset</button>');
            resetButton.on('click', function(event){
                event.preventDefault();
                $element.css({
                    width: '',
                    height: ''
                });
                finishEdit();
            });
            buttonGroup.append(fullButton);
            buttonGroup.append(halfButton);
            buttonGroup.append(quartButton);
            buttonGroup.append(resetButton);
            container.append(buttonGroup);

            buttonGroup = angular.element('<div class="btn-group" style="padding-right: 6px;">');
            var floatLeft = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" unselectable="on" tabindex="-1"><i class="fa fa-align-left"></i></button>');
            floatLeft.on('click', function(event){
                event.preventDefault();
                // webkit
                $element.css('float', 'left');
                // firefox
                $element.css('cssFloat', 'left');
                // IE < 8
                $element.css('styleFloat', 'left');
                finishEdit();
            });
            var floatRight = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" unselectable="on" tabindex="-1"><i class="fa fa-align-right"></i></button>');
            floatRight.on('click', function(event){
                event.preventDefault();
                // webkit
                $element.css('float', 'right');
                // firefox
                $element.css('cssFloat', 'right');
                // IE < 8
                $element.css('styleFloat', 'right');
                finishEdit();
            });
            var floatNone = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" unselectable="on" tabindex="-1"><i class="fa fa-align-justify"></i></button>');
            floatNone.on('click', function(event){
                event.preventDefault();
                // webkit
                $element.css('float', '');
                // firefox
                $element.css('cssFloat', '');
                // IE < 8
                $element.css('styleFloat', '');
                finishEdit();
            });
            buttonGroup.append(floatLeft);
            buttonGroup.append(floatNone);
            buttonGroup.append(floatRight);
            container.append(buttonGroup);

            buttonGroup = angular.element('<div class="btn-group">');
            var remove = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" unselectable="on" tabindex="-1"><i class="fa fa-trash-o"></i></button>');
            remove.on('click', function(event){
                event.preventDefault();
                $element.remove();
                finishEdit();
            });
            buttonGroup.append(remove);
            container.append(buttonGroup);

            editorScope.showPopover($element);
            editorScope.showResizeOverlay($element);
        },
        aOnSelectAction: function(event, $element, editorScope){
            // setup the editor toolbar
            // Credit to the work at http://hackerwins.github.io/summernote/ for this editbar logic
            event.preventDefault();
            editorScope.displayElements.popover.css('width', '436px');
            var container = editorScope.displayElements.popoverContainer;
            container.empty();
            container.css('line-height', '28px');
            var link = angular.element('<a href="' + $element.attr('href') + '" target="_blank">' + $element.attr('href') + '</a>');
            link.css({
                'display': 'inline-block',
                'max-width': '200px',
                'overflow': 'hidden',
                'text-overflow': 'ellipsis',
                'white-space': 'nowrap',
                'vertical-align': 'middle'
            });
            container.append(link);
            var buttonGroup = angular.element('<div class="btn-group pull-right">');
            var reLinkButton = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" tabindex="-1" unselectable="on" title="' + taTranslations.editLink.reLinkButton.tooltip + '"><i class="fa fa-edit icon-edit"></i></button>');
            reLinkButton.on('click', function(event){
                event.preventDefault();
                var urlLink = $window.prompt(taTranslations.insertLink.dialogPrompt, $element.attr('href'));
                if(urlLink && urlLink !== '' && urlLink !== 'http://'){
                    $element.attr('href', urlLink);
                    editorScope.updateTaBindtaTextElement();
                }
                editorScope.hidePopover();
            });
            buttonGroup.append(reLinkButton);
            var unLinkButton = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" tabindex="-1" unselectable="on" title="' + taTranslations.editLink.unLinkButton.tooltip + '"><i class="fa fa-unlink icon-unlink"></i></button>');
            // directly before this click event is fired a digest is fired off whereby the reference to $element is orphaned off
            unLinkButton.on('click', function(event){
                event.preventDefault();
                $element.replaceWith($element.contents());
                editorScope.updateTaBindtaTextElement();
                editorScope.hidePopover();
            });
            buttonGroup.append(unLinkButton);
            var targetToggle = angular.element('<button type="button" class="btn btn-default btn-sm btn-small" tabindex="-1" unselectable="on">' + taTranslations.editLink.targetToggle.buttontext + '</button>');
            if($element.attr('target') === '_blank'){
                targetToggle.addClass('active');
            }
            targetToggle.on('click', function(event){
                event.preventDefault();
                $element.attr('target', ($element.attr('target') === '_blank') ? '' : '_blank');
                targetToggle.toggleClass('active');
                editorScope.updateTaBindtaTextElement();
            });
            buttonGroup.append(targetToggle);
            container.append(buttonGroup);
            editorScope.showPopover($element);
        },
        extractYoutubeVideoId: function(url) {
            var re = /(?:youtube(?:-nocookie)?\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i;
            var match = url.match(re);
            return (match && match[1]) || null;
        }
    };
}])
.run(['taRegisterTool', '$window', 'taTranslations', 'taSelection', 'taToolFunctions', '$sanitize', 'taOptions', '$log',
    function(taRegisterTool, $window, taTranslations, taSelection, taToolFunctions, $sanitize, taOptions, $log){
    // test for the version of $sanitize that is in use
    // You can disable this check by setting taOptions.textAngularSanitize == false
    var gv = {}; $sanitize('', gv);
    /* istanbul ignore next, throws error */
    if ((taOptions.forceTextAngularSanitize===true) && (gv.version !== 'taSanitize')) {
        throw angular.$$minErr('textAngular')("textAngularSetup", "The textAngular-sanitize provider has been replaced by another -- have you included angular-sanitize by mistake?");
    }
    taRegisterTool("html", {
        iconclass: 'fa fa-code',
        tooltiptext: taTranslations.html.tooltip,
        action: function(){
            this.$editor().switchView();
        },
        activeState: function(){
            return this.$editor().showHtml;
        }
    });
    // add the Header tools
    // convenience functions so that the loop works correctly
    var _retActiveStateFunction = function(q){
        return function(){ return this.$editor().queryFormatBlockState(q); };
    };
    var headerAction = function(){
        return this.$editor().wrapSelection("formatBlock", "<" + this.name.toUpperCase() +">");
    };
    angular.forEach(['h1','h2','h3','h4','h5','h6'], function(h){
        taRegisterTool(h.toLowerCase(), {
            buttontext: h.toUpperCase(),
            tooltiptext: taTranslations.heading.tooltip + h.charAt(1),
            action: headerAction,
            activeState: _retActiveStateFunction(h.toLowerCase())
        });
    });
    taRegisterTool('p', {
        buttontext: 'P',
        tooltiptext: taTranslations.p.tooltip,
        action: function(){
            return this.$editor().wrapSelection("formatBlock", "<P>");
        },
        activeState: function(){ return this.$editor().queryFormatBlockState('p'); }
    });
    // key: pre -> taTranslations[key].tooltip, taTranslations[key].buttontext
    taRegisterTool('pre', {
        buttontext: 'pre',
        tooltiptext: taTranslations.pre.tooltip,
        action: function(){
            return this.$editor().wrapSelection("formatBlock", "<PRE>");
        },
        activeState: function(){ return this.$editor().queryFormatBlockState('pre'); }
    });
    taRegisterTool('ul', {
        iconclass: 'fa fa-list-ul',
        tooltiptext: taTranslations.ul.tooltip,
        action: function(){
            return this.$editor().wrapSelection("insertUnorderedList", null);
        },
        activeState: function(){ return this.$editor().queryCommandState('insertUnorderedList'); }
    });
    taRegisterTool('ol', {
        iconclass: 'fa fa-list-ol',
        tooltiptext: taTranslations.ol.tooltip,
        action: function(){
            return this.$editor().wrapSelection("insertOrderedList", null);
        },
        activeState: function(){ return this.$editor().queryCommandState('insertOrderedList'); }
    });
    taRegisterTool('quote', {
        iconclass: 'fa fa-quote-right',
        tooltiptext: taTranslations.quote.tooltip,
        action: function(){
            return this.$editor().wrapSelection("formatBlock", "<BLOCKQUOTE>");
        },
        activeState: function(){ return this.$editor().queryFormatBlockState('blockquote'); }
    });
    taRegisterTool('undo', {
        iconclass: 'fa fa-undo',
        tooltiptext: taTranslations.undo.tooltip,
        action: function(){
            return this.$editor().wrapSelection("undo", null);
        }
    });
    taRegisterTool('redo', {
        iconclass: 'fa fa-repeat',
        tooltiptext: taTranslations.redo.tooltip,
        action: function(){
            return this.$editor().wrapSelection("redo", null);
        }
    });
    taRegisterTool('bold', {
        iconclass: 'fa fa-bold',
        tooltiptext: taTranslations.bold.tooltip,
        action: function(){
            return this.$editor().wrapSelection("bold", null);
        },
        activeState: function(){
            return this.$editor().queryCommandState('bold');
        },
        commandKeyCode: 98
    });
    taRegisterTool('justifyLeft', {
        iconclass: 'fa fa-align-left',
        tooltiptext: taTranslations.justifyLeft.tooltip,
        action: function(){
            return this.$editor().wrapSelection("justifyLeft", null);
        },
        activeState: function(commonElement){
            /* istanbul ignore next: */
            if (commonElement && commonElement.nodeName === '#document') return false;
            var result = false;
            if (commonElement) {
                // commonELement.css('text-align') can throw an error 'Cannot read property 'defaultView' of null' in rare conditions
                // so we do try catch here...
                try {
                    result =
                        commonElement.css('text-align') === 'left' ||
                        commonElement.attr('align') === 'left' ||
                        (
                            commonElement.css('text-align') !== 'right' &&
                            commonElement.css('text-align') !== 'center' &&
                            commonElement.css('text-align') !== 'justify' && !this.$editor().queryCommandState('justifyRight') && !this.$editor().queryCommandState('justifyCenter')
                        ) && !this.$editor().queryCommandState('justifyFull');
                } catch(e) {
                    /* istanbul ignore next: error handler */
                    //console.log(e);
                    result = false;
                }
            }
            result = result || this.$editor().queryCommandState('justifyLeft');
            return result;
        }
    });
    taRegisterTool('justifyRight', {
        iconclass: 'fa fa-align-right',
        tooltiptext: taTranslations.justifyRight.tooltip,
        action: function(){
            return this.$editor().wrapSelection("justifyRight", null);
        },
        activeState: function(commonElement){
            /* istanbul ignore next: */
            if (commonElement && commonElement.nodeName === '#document') return false;
            var result = false;
            if(commonElement) {
                // commonELement.css('text-align') can throw an error 'Cannot read property 'defaultView' of null' in rare conditions
                // so we do try catch here...
                try {
                    result = commonElement.css('text-align') === 'right';
                } catch(e) {
                    /* istanbul ignore next: error handler */
                    //console.log(e);
                    result = false;
                }
            }
            result = result || this.$editor().queryCommandState('justifyRight');
            return result;
        }
    });
    taRegisterTool('justifyFull', {
        iconclass: 'fa fa-align-justify',
        tooltiptext: taTranslations.justifyFull.tooltip,
        action: function(){
            return this.$editor().wrapSelection("justifyFull", null);
        },
        activeState: function(commonElement){
            var result = false;
            if(commonElement) {
                // commonELement.css('text-align') can throw an error 'Cannot read property 'defaultView' of null' in rare conditions
                // so we do try catch here...
                try {
                    result = commonElement.css('text-align') === 'justify';
                } catch(e) {
                    /* istanbul ignore next: error handler */
                    //console.log(e);
                    result = false;
                }
            }
            result = result || this.$editor().queryCommandState('justifyFull');
            return result;
        }
    });
    taRegisterTool('justifyCenter', {
        iconclass: 'fa fa-align-center',
        tooltiptext: taTranslations.justifyCenter.tooltip,
        action: function(){
            return this.$editor().wrapSelection("justifyCenter", null);
        },
        activeState: function(commonElement){
            /* istanbul ignore next: */
            if (commonElement && commonElement.nodeName === '#document') return false;
            var result = false;
            if(commonElement) {
                // commonELement.css('text-align') can throw an error 'Cannot read property 'defaultView' of null' in rare conditions
                // so we do try catch here...
                try {
                    result = commonElement.css('text-align') === 'center';
                } catch(e) {
                    /* istanbul ignore next: error handler */
                    //console.log(e);
                    result = false;
                }

            }
            result = result || this.$editor().queryCommandState('justifyCenter');
            return result;
        }
    });
    taRegisterTool('indent', {
        iconclass: 'fa fa-indent',
        tooltiptext: taTranslations.indent.tooltip,
        action: function(){
            return this.$editor().wrapSelection("indent", null);
        },
        activeState: function(){
            return this.$editor().queryFormatBlockState('blockquote');
        },
        commandKeyCode: 'TabKey'
    });
    taRegisterTool('outdent', {
        iconclass: 'fa fa-outdent',
        tooltiptext: taTranslations.outdent.tooltip,
        action: function(){
            return this.$editor().wrapSelection("outdent", null);
        },
        activeState: function(){
            return false;
        },
        commandKeyCode: 'ShiftTabKey'
    });
    taRegisterTool('italics', {
        iconclass: 'fa fa-italic',
        tooltiptext: taTranslations.italic.tooltip,
        action: function(){
            return this.$editor().wrapSelection("italic", null);
        },
        activeState: function(){
            return this.$editor().queryCommandState('italic');
        },
        commandKeyCode: 105
    });
    taRegisterTool('underline', {
        iconclass: 'fa fa-underline',
        tooltiptext: taTranslations.underline.tooltip,
        action: function(){
            return this.$editor().wrapSelection("underline", null);
        },
        activeState: function(){
            return this.$editor().queryCommandState('underline');
        },
        commandKeyCode: 117
    });
    taRegisterTool('strikeThrough', {
        iconclass: 'fa fa-strikethrough',
        tooltiptext: taTranslations.strikeThrough.tooltip,
        action: function(){
            return this.$editor().wrapSelection("strikeThrough", null);
        },
        activeState: function(){
            return document.queryCommandState('strikeThrough');
        }
    });
    taRegisterTool('clear', {
        iconclass: 'fa fa-ban',
        tooltiptext: taTranslations.clear.tooltip,
        action: function(deferred, restoreSelection){
            var i, selectedElements, elementsSeen;

            this.$editor().wrapSelection("removeFormat", null);
            var possibleNodes = angular.element(taSelection.getSelectionElement());
            selectedElements = taSelection.getAllSelectedElements();
            //$log.log('selectedElements:', selectedElements);
            // remove lists
            var removeListElements = function(list, pe){
                list = angular.element(list);
                var prevElement = pe;
                if (!pe) {
                    prevElement = list;
                }
                angular.forEach(list.children(), function(liElem){
                    if (liElem.tagName.toLowerCase() === 'ul' ||
                        liElem.tagName.toLowerCase() === 'ol') {
                        prevElement = removeListElements(liElem, prevElement);
                    } else {
                        var newElem = angular.element('<p></p>');
                        newElem.html(angular.element(liElem).html());
                        prevElement.after(newElem);
                        prevElement = newElem;
                    }
                });
                list.remove();
                return prevElement;
            };

            angular.forEach(selectedElements, function(element) {
                if (element.nodeName.toLowerCase() === 'ul' ||
                    element.nodeName.toLowerCase() === 'ol') {
                    //console.log('removeListElements', element);
                    removeListElements(element);
                }
            });

            angular.forEach(possibleNodes.find("ul"), removeListElements);
            angular.forEach(possibleNodes.find("ol"), removeListElements);

            // clear out all class attributes. These do not seem to be cleared via removeFormat
            var $editor = this.$editor();
            var recursiveRemoveClass = function(node){
                node = angular.element(node);
                /* istanbul ignore next: this is not triggered in tests any longer since we now never select the whole displayELement */
                if(node[0] !== $editor.displayElements.text[0]) {
                    node.removeAttr('class');
                }
                angular.forEach(node.children(), recursiveRemoveClass);
            };
            angular.forEach(possibleNodes, recursiveRemoveClass);
            // check if in list. If not in list then use formatBlock option
            if(possibleNodes[0] && possibleNodes[0].tagName.toLowerCase() !== 'li' &&
                possibleNodes[0].tagName.toLowerCase() !== 'ol' &&
                possibleNodes[0].tagName.toLowerCase() !== 'ul' &&
                possibleNodes[0].getAttribute("contenteditable") !== "true") {
                this.$editor().wrapSelection("formatBlock", "default");
            }
            restoreSelection();
        }
    });

        /* jshint -W099 */
    /****************************
     //  we don't use this code - since the previous way CLEAR is expected to work does not clear partially selected <li>

     var removeListElement = function(listE){
                console.log(listE);
                var _list = listE.parentNode.childNodes;
                console.log('_list', _list);
                var _preLis = [], _postLis = [], _found = false;
                for (i = 0; i < _list.length; i++) {
                    if (_list[i] === listE) {
                        _found = true;
                    } else if (!_found) _preLis.push(_list[i]);
                    else _postLis.push(_list[i]);
                }
                var _parent = angular.element(listE.parentNode);
                var newElem = angular.element('<p></p>');
                newElem.html(angular.element(listE).html());
                if (_preLis.length === 0 || _postLis.length === 0) {
                    if (_postLis.length === 0) _parent.after(newElem);
                    else _parent[0].parentNode.insertBefore(newElem[0], _parent[0]);

                    if (_preLis.length === 0 && _postLis.length === 0) _parent.remove();
                    else angular.element(listE).remove();
                } else {
                    var _firstList = angular.element('<' + _parent[0].tagName + '></' + _parent[0].tagName + '>');
                    var _secondList = angular.element('<' + _parent[0].tagName + '></' + _parent[0].tagName + '>');
                    for (i = 0; i < _preLis.length; i++) _firstList.append(angular.element(_preLis[i]));
                    for (i = 0; i < _postLis.length; i++) _secondList.append(angular.element(_postLis[i]));
                    _parent.after(_secondList);
                    _parent.after(newElem);
                    _parent.after(_firstList);
                    _parent.remove();
                }
                taSelection.setSelectionToElementEnd(newElem[0]);
            };

     elementsSeen = [];
     if (selectedElements.length !==0) console.log(selectedElements);
     angular.forEach(selectedElements, function (element) {
                if (elementsSeen.indexOf(element) !== -1 || elementsSeen.indexOf(element.parentElement) !== -1) {
                    return;
                }
                elementsSeen.push(element);
                if (element.nodeName.toLowerCase() === 'li') {
                    console.log('removeListElement', element);
                    removeListElement(element);
                }
                else if (element.parentElement && element.parentElement.nodeName.toLowerCase() === 'li') {
                    console.log('removeListElement', element.parentElement);
                    elementsSeen.push(element.parentElement);
                    removeListElement(element.parentElement);
                }
            });
     **********************/

    /**********************
     if(possibleNodes[0].tagName.toLowerCase() === 'li'){
                var _list = possibleNodes[0].parentNode.childNodes;
                var _preLis = [], _postLis = [], _found = false;
                for(i = 0; i < _list.length; i++){
                    if(_list[i] === possibleNodes[0]){
                        _found = true;
                    }else if(!_found) _preLis.push(_list[i]);
                    else _postLis.push(_list[i]);
                }
                var _parent = angular.element(possibleNodes[0].parentNode);
                var newElem = angular.element('<p></p>');
                newElem.html(angular.element(possibleNodes[0]).html());
                if(_preLis.length === 0 || _postLis.length === 0){
                    if(_postLis.length === 0) _parent.after(newElem);
                    else _parent[0].parentNode.insertBefore(newElem[0], _parent[0]);

                    if(_preLis.length === 0 && _postLis.length === 0) _parent.remove();
                    else angular.element(possibleNodes[0]).remove();
                }else{
                    var _firstList = angular.element('<'+_parent[0].tagName+'></'+_parent[0].tagName+'>');
                    var _secondList = angular.element('<'+_parent[0].tagName+'></'+_parent[0].tagName+'>');
                    for(i = 0; i < _preLis.length; i++) _firstList.append(angular.element(_preLis[i]));
                    for(i = 0; i < _postLis.length; i++) _secondList.append(angular.element(_postLis[i]));
                    _parent.after(_secondList);
                    _parent.after(newElem);
                    _parent.after(_firstList);
                    _parent.remove();
                }
                taSelection.setSelectionToElementEnd(newElem[0]);
            }
     *******************/


    /* istanbul ignore next: if it's javascript don't worry - though probably should show some kind of error message */
    var blockJavascript = function (link) {
        if (link.toLowerCase().indexOf('javascript')!==-1) {
            return true;
        }
        return false;
    };

    taRegisterTool('insertImage', {
        iconclass: 'fa fa-picture-o',
        tooltiptext: taTranslations.insertImage.tooltip,
        action: function(){
            var imageLink;
            imageLink = $window.prompt(taTranslations.insertImage.dialogPrompt, 'http://');
            if(imageLink && imageLink !== '' && imageLink !== 'http://'){
                /* istanbul ignore next: don't know how to test this... since it needs a dialogPrompt */
                // block javascript here
                if (!blockJavascript(imageLink)) {
                    if (taSelection.getSelectionElement().tagName && taSelection.getSelectionElement().tagName.toLowerCase() === 'a') {
                        // due to differences in implementation between FireFox and Chrome, we must move the
                        // insertion point past the <a> element, otherwise FireFox inserts inside the <a>
                        // With this change, both FireFox and Chrome behave the same way!
                        taSelection.setSelectionAfterElement(taSelection.getSelectionElement());
                    }
                    // In the past we used the simple statement:
                    //return this.$editor().wrapSelection('insertImage', imageLink, true);
                    //
                    // However on Firefox only, when the content is empty this is a problem
                    // See Issue #1201
                    // Investigation reveals that Firefox only inserts a <p> only!!!!
                    // So now we use insertHTML here and all is fine.
                    // NOTE: this is what 'insertImage' is supposed to do anyway!
                    var embed = '<img src="' + imageLink + '">';
                    return this.$editor().wrapSelection('insertHTML', embed, true);
                }
            }
        },
        onElementSelect: {
            element: 'img',
            action: taToolFunctions.imgOnSelectAction
        }
    });
    taRegisterTool('insertVideo', {
        iconclass: 'fa fa-youtube-play',
        tooltiptext: taTranslations.insertVideo.tooltip,
        action: function(){
            var urlPrompt;
            urlPrompt = $window.prompt(taTranslations.insertVideo.dialogPrompt, 'https://');
            // block javascript here
            /* istanbul ignore else: if it's javascript don't worry - though probably should show some kind of error message */
            if (!blockJavascript(urlPrompt)) {

                if (urlPrompt && urlPrompt !== '' && urlPrompt !== 'https://') {

                    videoId = taToolFunctions.extractYoutubeVideoId(urlPrompt);

                    /* istanbul ignore else: if it's invalid don't worry - though probably should show some kind of error message */
                    if (videoId) {
                        // create the embed link
                        var urlLink = "https://www.youtube.com/embed/" + videoId;
                        // create the HTML
                        // for all options see: http://stackoverflow.com/questions/2068344/how-do-i-get-a-youtube-video-thumbnail-from-the-youtube-api
                        // maxresdefault.jpg seems to be undefined on some.
                        var embed = '<img class="ta-insert-video" src="https://img.youtube.com/vi/' + videoId + '/hqdefault.jpg" ta-insert-video="' + urlLink + '" contenteditable="false" allowfullscreen="true" frameborder="0" />';
                        /* istanbul ignore next: don't know how to test this... since it needs a dialogPrompt */
                        if (taSelection.getSelectionElement().tagName && taSelection.getSelectionElement().tagName.toLowerCase() === 'a') {
                            // due to differences in implementation between FireFox and Chrome, we must move the
                            // insertion point past the <a> element, otherwise FireFox inserts inside the <a>
                            // With this change, both FireFox and Chrome behave the same way!
                            taSelection.setSelectionAfterElement(taSelection.getSelectionElement());
                        }
                        // insert
                        return this.$editor().wrapSelection('insertHTML', embed, true);
                    }
                }
            }
        },
        onElementSelect: {
            element: 'img',
            onlyWithAttrs: ['ta-insert-video'],
            action: taToolFunctions.imgOnSelectAction
        }
    });
    taRegisterTool('insertLink', {
        tooltiptext: taTranslations.insertLink.tooltip,
        iconclass: 'fa fa-link',
        action: function(){
            var urlLink;
            // if this link has already been set, we need to just edit the existing link
            /* istanbul ignore if: we do not test this */
            if (taSelection.getSelectionElement().tagName && taSelection.getSelectionElement().tagName.toLowerCase() === 'a') {
                urlLink = $window.prompt(taTranslations.insertLink.dialogPrompt, taSelection.getSelectionElement().href);
            } else {
                urlLink = $window.prompt(taTranslations.insertLink.dialogPrompt, 'http://');
            }
            if(urlLink && urlLink !== '' && urlLink !== 'http://'){
                // block javascript here
                /* istanbul ignore else: if it's javascript don't worry - though probably should show some kind of error message */
                if (!blockJavascript(urlLink)) {
                    return this.$editor().wrapSelection('createLink', urlLink, true);
                }
            }
        },
        activeState: function(commonElement){
            if(commonElement) return commonElement[0].tagName === 'A';
            return false;
        },
        onElementSelect: {
            element: 'a',
            action: taToolFunctions.aOnSelectAction
        }
    });
    taRegisterTool('wordcount', {
        display: '<div id="toolbarWC" style="display:block; min-width:100px;">Words: <span ng-bind="wordcount"></span></div>',
        disabled: true,
        wordcount: 0,
        activeState: function(){ // this fires on keyup
            var textElement = this.$editor().displayElements.text;
            /* istanbul ignore next: will default to '' when undefined */
            var workingHTML = textElement[0].innerHTML || '';
            var noOfWords = 0;

            /* istanbul ignore if: will default to '' when undefined */
            if (workingHTML.replace(/\s*<[^>]*?>\s*/g, '') !== '') {
                if (workingHTML.trim() !== '') {
                    noOfWords = workingHTML.replace(/<\/?(b|i|em|strong|span|u|strikethrough|a|img|small|sub|sup|label)( [^>*?])?>/gi, '') // remove inline tags without adding spaces
                        .replace(/(<[^>]*?>\s*<[^>]*?>)/ig, ' ') // replace adjacent tags with possible space between with a space
                        .replace(/(<[^>]*?>)/ig, '') // remove any singular tags
                        .replace(/\s+/ig, ' ') // condense spacing
                        .match(/\S+/g).length; // count remaining non-space strings
                }
            }

            //Set current scope
            this.wordcount = noOfWords;
            //Set editor scope
            this.$editor().wordcount = noOfWords;

            return false;
        }
    });
    taRegisterTool('charcount', {
        display: '<div id="toolbarCC" style="display:block; min-width:120px;">Characters: <span ng-bind="charcount"></span></div>',
        disabled: true,
        charcount: 0,
        activeState: function(){ // this fires on keyup
            var textElement = this.$editor().displayElements.text;
            var sourceText = textElement[0].innerText || textElement[0].textContent; // to cover the non-jquery use case.

            // Caculate number of chars
            var noOfChars = sourceText.replace(/(\r\n|\n|\r)/gm,"").replace(/^\s+/g,' ').replace(/\s+$/g, ' ').length;
            //Set current scope
            this.charcount = noOfChars;
            //Set editor scope
            this.$editor().charcount = noOfChars;
            return false;
        }
    });
}]);

/*!
 * su-componentes
 * 
 * Version: 0.0.186 - 2026-08-05T12:25:13.879Z
 */


(function () {
    'use strict';


    angular.module('su-componentes', [
        'LocalStorageModule',
        'ngResource',
        'ngCookies',
        'ngAria',
        'ngCacheBuster',
        'ui.router',
        'ngAnimate',
        'ngMaterial',
        'ngMdIcons',
        'vAccordion',
        'ngSanitize',
        'angular-loading-bar',
        'ncy-angular-breadcrumb',
        'textAngular',
        'pascalprecht.translate',
        'md.data.table',
        'ngFileUpload',
        'infinite-scroll',
        'ui.mask',
        'ui.utils.masks',
        'idf.br-filters',
        'highcharts-ng',
        'ngMask',
        'angularMoment',
        'readMore',
	    'xeditable',
        'ui.sortable'
    ]);

})();

(function () {
    'use strict';


    var URL_AVATAR_ICONS = 'assets/img/icons/avatars.svg';
    var URL_ICON_MENU = 'assets/img/icons/menu.svg';
    var URL_ICON_SHARE = 'assets/img/icons/share.svg';
    var URL_ICON_PERSON = 'assets/img/icons/ic_person_48px.svg';
    var URL_LOGOUT = 'assets/img/icons/ic_power_settings_new_48px.svg';

    angular.module('su-componentes')
        .config(function ($httpProvider, $locationProvider, httpRequestInterceptorCacheBusterProvider) {
            $httpProvider.defaults.xsrfCookieName = 'CSRF-TOKEN';
            $httpProvider.defaults.xsrfHeaderName = 'X-CSRF-TOKEN';
            //Cache everything except rest api requests
            httpRequestInterceptorCacheBusterProvider.setMatchlist([/.*api.*/, /.*protected.*/], true);
            $httpProvider.interceptors.push('errorHandlerInterceptor');
            $locationProvider.hashPrefix('');
        })

        .config(['$urlMatcherFactoryProvider', function ($urlMatcherFactory) {
            $urlMatcherFactory.type('boolean', {
                name: 'boolean',
                decode: function (val) {
                    return val === true ? true : val === 'true' ? true : false;
                },
                encode: function (val) {
                    return val ? 1 : 0;
                },
                equals: function (a, b) {
                    return this.is(a) && a === b;
                },
                is: function (val) {
                    return [true, false, 0, 1].indexOf(val) >= 0;
                },
                pattern: /bool|true|0|1/
            });
        }])

        .config(function ($mdThemingProvider) {
            $mdThemingProvider.theme('success-toast');
            $mdThemingProvider.theme('default').primaryPalette('green');
        })

        .config(function ($compileProvider) {
            $compileProvider.aHrefSanitizationWhitelist(/^\s*(https?|file|ftp|blob):|data:image\//);
        })

        .config(function ($mdDateLocaleProvider) {

            var myShortMonths = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

            $mdDateLocaleProvider.shortMonths = myShortMonths;

            $mdDateLocaleProvider.shortDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
            $mdDateLocaleProvider.firstDayOfWeek = 1;

            $mdDateLocaleProvider.monthHeaderFormatter = function (date) {
                return myShortMonths[date.getMonth()] + ' ' + date.getFullYear();
            };

            $mdDateLocaleProvider.weekNumberFormatter = function (weekNumber) {
                return 'Semana ' + weekNumber;
            };


        })

        .config(function ($mdIconProvider) {
            $mdIconProvider
                .defaultIconSet(URL_AVATAR_ICONS, 128)
                .icon('menu', URL_ICON_MENU, 24)
                .icon('share', URL_ICON_SHARE, 24)
                .icon('person', URL_LOGOUT, 48)
                .icon('person', URL_ICON_PERSON, 48);
        })
        .config(['$mdThemingProvider', function ($mdThemingProvider) {
            var customPrimary = {
                '50': '#ff7b82',
                '100': '#ff626a',
                '200': '#ff4852',
                '300': '#ff2f3a',
                '400': '#ff1522',
                '500': '#fb000d',
                '600': '#e1000c',
                '700': '#c8000a',
                '800': '#ae0009',
                '900': '#950008',
                'A100': '#ff959a',
                'A200': '#ffaeb3',
                'A400': '#ffc8cb',
                'A700': '#7b0006'
            };
            $mdThemingProvider.definePalette('customPrimary',
                customPrimary);

        }])
        .config(['$translateProvider', function ($translateProvider) {

            $translateProvider.useLoader('$translatePartialLoader', {
                urlTemplate: '{part}/{lang}.json'
            });

            $translateProvider.useLoaderCache('$templateCache');

            $translateProvider.preferredLanguage('pt-BR');
            $translateProvider.useSanitizeValueStrategy('escapeParameters');

        }])
        .config(function ($mdDateLocaleProvider, moment) {

            $mdDateLocaleProvider.formatDate = function (date) {
                return date ? moment(date).format('DD/MM/YYYY') : '';
            };

            $mdDateLocaleProvider.parseDate = function (dateString) {
                if (dateString == null || dateString.length === 0) {
                    return null;
                }

                var m = moment(dateString, 'DD/MM/YYYY', true);
                return m.isValid() ? m.toDate() : '';
            };

        })
        .config(function ($breadcrumbProvider) {
            $breadcrumbProvider.setOptions({
                template: '<div id="breadcrumb"><ul class="breadcrumb"> <span class="voce-esta-aqui">VOCÊ ESTÁ AQUI:</span> <li class="migalha-pao" ng-repeat="step in steps | limitTo:(steps.length-1)"> <a href="{{step.ncyBreadcrumbLink}}" ng-bind-html="step.ncyBreadcrumbLabel"></a> </li><li class="migalha-pao" ng-repeat="step in steps | limitTo:-1" class="active"> <span ng-bind-html="step.ncyBreadcrumbLabel"></span> </li></ul> </div>'
            });
        })
        .config(function ($provide) {

            $provide.decorator('taOptions', ['$delegate', function (taOptions) {
                taOptions.forceTextAngularSanitize = true;
                taOptions.keyMappings = [];
                taOptions.toolbar = [
                    ['h1', 'h2', 'h3', 'h4', 'p'],
                    ['bold', 'italics', 'underline', 'clear'],
                    ['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'],
                    ['html', 'insertImage', 'insertLink']
                ];
                taOptions.classes = {
                    focussed: 'focussed',
                    toolbar: 'btn-toolbar',
                    toolbarGroup: 'btn-group',
                    toolbarButton: 'btn btn-default',
                    toolbarButtonActive: 'active',
                    disabled: 'disabled',
                    textEditor: 'form-control',
                    htmlEditor: 'form-control'
                };
                return taOptions;
            }]);

        });

})();

(function () {
    'use strict';


    angular.module('su-componentes')
        .run(function ($rootScope, $location, $window) {
            $rootScope.$on('$stateChangeSuccess', function (event, toState) {
                if (toState.data && toState.data.pageTitle) {
                    $window.document.title = toState.data.pageTitle;
                }
            });
        });
})();

(function () {
    'use strict';


    angular.module('su-componentes')
        .value('urlPrefixo', '');
})();

(function () {
    'use strict';
    angular.module('su-componentes')
        .component('cabecalho', {
            templateUrl: 'scripts-componentes/cabecalho/cabecalho.html',
            controller: function($scope, $location, urlPrefixo){
                $scope.urlPrefixo = urlPrefixo;

                $scope.urlHome = $location.protocol()+'://'+$location.host();
            }
        });
})();


//http://weblogs.asp.net/dwahlin/creating-custom-angularjs-directives-part-i-the-fundamentals

(function () {

    'use strict';

    var directive = function () {
        return {
            restrict: 'EA', //E = element, A = attribute, C = class, M = comment
            scope: {
                //@ reads the attribute value, = provides two-way binding, & works with functions
                id: '=ngModel'
            },
            templateUrl: 'scripts-componentes/diretivas/mapa.html',
            //controller: controllerFunction, //Embed a custom controller in the directive
            link: function () {


            } //DOM manipulation
        };
    };

    angular.module('su-componentes')
        .directive('mapa',[directive]);

}());

/*
 * Angular Material Data Table
 * https://github.com/daniel-nagy/md-data-table
 * @license MIT
 * v0.10.4

 * NÃO ALTERAR OU APAGAR ESTE ARQUIVO.
 * NECESSÁRIO PARA TRADUZIR OS COMPONENTES DE PAGINAÇÃO
 * @RAPHAEL.SOUZA
*/

(function (window, angular) {
'use strict';

angular.module('md.table.templates', ['md-table-pagination.html', 'md-table-progress.html', 'arrow-up.svg', 'navigate-before.svg', 'navigate-first.svg', 'navigate-last.svg', 'navigate-next.svg']);

angular.module('md-table-pagination.html', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('md-table-pagination.html',
    '<div class="page-select" ng-if="$pagination.showPageSelect()">\n' +
    '  <div class="label">{{$pagination.label.page}}</div>\n' +
    '\n' +
    '  <md-select virtual-page-select total="{{$pagination.pages()}}" class="md-table-select" ng-model="$pagination.page" md-container-class="md-pagination-select" ng-change="$pagination.onPaginationChange()" ng-disabled="$pagination.disabled" aria-label="Page">\n' +
    '    <md-content>\n' +
    '      <md-option ng-repeat="page in $pageSelect.pages" ng-value="page">{{page}}</md-option>\n' +
    '    </md-content>\n' +
    '  </md-select>\n' +
    '</div>\n' +
    '\n' +
    '<div class="limit-select" ng-if="$pagination.limitOptions">\n' +
    '  <div class="label">{{$pagination.label.rowsPerPage}}</div>\n' +
    '\n' +
    '  <md-select class="md-table-select" ng-model="$pagination.limit" md-container-class="md-pagination-select" ng-disabled="$pagination.disabled" aria-label="Rows" placeholder="{{ $pagination.limitOptions[0] }}">\n' +
    '    <md-option ng-repeat="rows in $pagination.limitOptions" ng-value="rows">{{rows}}</md-option>\n' +
    '  </md-select>\n' +
    '</div>\n' +
    '\n' +
    '<div class="buttons">\n' +
    '  <div class="label">{{$pagination.min()}} - {{$pagination.max()}} {{$pagination.label.of}} {{$pagination.total}}</div>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-if="$pagination.showBoundaryLinks()" ng-click="$pagination.first()" ng-disabled="$pagination.disabled || !$pagination.hasPrevious()" aria-label="First">\n' +
    '    <md-icon md-svg-icon="navigate-first.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-click="$pagination.previous()" ng-disabled="$pagination.disabled || !$pagination.hasPrevious()" aria-label="Previous">\n' +
    '    <md-icon md-svg-icon="navigate-before.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-click="$pagination.next()" ng-disabled="$pagination.disabled || !$pagination.hasNext()" aria-label="Next">\n' +
    '    <md-icon md-svg-icon="navigate-next.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-if="$pagination.showBoundaryLinks()" ng-click="$pagination.last()" ng-disabled="$pagination.disabled || !$pagination.hasNext()" aria-label="Last">\n' +
    '    <md-icon md-svg-icon="navigate-last.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '</div>');
}]);

angular.module('md-table-progress.html', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('md-table-progress.html',
    '<tr>\n' +
    '  <th colspan="{{columnCount()}}">\n' +
    '    <md-progress-linear ng-show="deferred()" md-mode="indeterminate"></md-progress-linear>\n' +
    '  </th>\n' +
    '</tr>');
}]);

angular.module('arrow-up.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('arrow-up.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"/></svg>');
}]);

angular.module('navigate-before.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-before.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>');
}]);

angular.module('navigate-first.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-first.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M7 6 v12 h2 v-12 h-2z M17.41 7.41L16 6l-6 6 6 6 1.41-1.41L12.83 12z"/></svg>');
}]);

angular.module('navigate-last.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-last.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M15 6 v12 h2 v-12 h-2z M8 6L6.59 7.41 11.17 12l-4.58 4.59L8 18l6-6z"/></svg>');
}]);

angular.module('navigate-next.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-next.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>');
}]);


angular.module('md.data.table', ['md.table.templates']);

angular.module('md.data.table').directive('mdBody', mdBody);

function mdBody() {

  function compile(tElement) {
    tElement.addClass('md-body');
  }

  return {
    compile: compile,
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdCell', mdCell);

function mdCell() {
  
  function compile(tElement) {
    var select = tElement.find('md-select');
    
    if(select.length) {
      select.addClass('md-table-select').attr('md-container-class', 'md-table-select');
    }
    
    tElement.addClass('md-cell');
    
    return postLink;
  }
  
  // empty controller to be bind properties to in postLink function
  function Controller() {
    
  }
  
  function postLink(scope, element, attrs, ctrls) {
    var select = element.find('md-select');
    var cellCtrl = ctrls.shift();
    var tableCtrl = ctrls.shift();
    
    if(attrs.ngClick) {
      element.addClass('md-clickable');
    }
    
    if(select.length) {
      select.on('click', function (event) {
        event.stopPropagation();
      });
      
      element.addClass('md-clickable').on('click', function (event) {
        event.stopPropagation();
        select[0].click();
      });
    }
    
    cellCtrl.getTable = tableCtrl.getElement;
    
    function getColumn() {
      return tableCtrl.$$columns[getIndex()];
    }
    
    function getIndex() {
      return Array.prototype.indexOf.call(element.parent().children(), element[0]);
    }
    
    scope.$watch(getColumn, function (column) {
      if(!column) {
        return;
      }
      
      if(column.numeric) {
        element.addClass('md-numeric');
      } else {
        element.removeClass('md-numeric');
      }
    });
  }
  
  return {
    controller: Controller,
    compile: compile,
    require: ['mdCell', '^^mdTable'],
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdColumn', mdColumn);

function mdColumn($compile, $mdUtil) {
  
  function compile(tElement) {
    tElement.addClass('md-column');
    return postLink;
  }

  function postLink(scope, element, attrs, ctrls) {
    var headCtrl = ctrls.shift();
    var tableCtrl = ctrls.shift();
    
    function attachSortIcon() {
      var sortIcon = angular.element('<md-icon md-svg-icon="arrow-up.svg">');
      
      $compile(sortIcon.addClass('md-sort-icon').attr('ng-class', 'getDirection()'))(scope);
      
      if(element.hasClass('md-numeric')) {
        element.prepend(sortIcon);
      } else {
        element.append(sortIcon);
      }
    }
    
    function detachSortIcon() {
      Array.prototype.some.call(element.find('md-icon'), function (icon) {
        return icon.classList.contains('md-sort-icon') && element[0].removeChild(icon);
      });
    }
    
    function disableSorting() {
      detachSortIcon();
      element.removeClass('md-sort').off('click', setOrder);
    }
    
    function enableSorting() {
      attachSortIcon();
      element.addClass('md-sort').on('click', setOrder);
    }
    
    function getIndex() {
      return Array.prototype.indexOf.call(element.parent().children(), element[0]);
    }
    
    function isActive() {
      return scope.orderBy && (headCtrl.order === scope.orderBy || headCtrl.order === '-' + scope.orderBy);
    }
    
    function isNumeric() {
      return attrs.mdNumeric === '' || scope.numeric;
    }
    
    function setOrder() {
      scope.$applyAsync(function () {
        if(isActive()) {
          headCtrl.order = scope.getDirection() === 'md-asc' ? '-' + scope.orderBy : scope.orderBy;
        } else {
          headCtrl.order = scope.getDirection() === 'md-asc' ? scope.orderBy : '-' + scope.orderBy;
        }
        
        if(angular.isFunction(headCtrl.onReorder)) {
          $mdUtil.nextTick(function () {
            headCtrl.onReorder(headCtrl.order);
          });
        }
      });
    }
    
    function updateColumn(index, column) {
      tableCtrl.$$columns[index] = column;
      
      if(column.numeric) {
        element.addClass('md-numeric');
      } else {
        element.removeClass('md-numeric');
      }
    }
    
    scope.getDirection = function () {
      if(isActive()) {
        return headCtrl.order.charAt(0) === '-' ? 'md-desc' : 'md-asc';
      }
      
      return attrs.mdDesc === '' || scope.$eval(attrs.mdDesc) ? 'md-desc' : 'md-asc';
    };
    
    scope.$watch(isActive, function (active) {
      if(active) {
        element.addClass('md-active');
      } else {
        element.removeClass('md-active');
      }
    });
    
    scope.$watch(getIndex, function (index) {
      updateColumn(index, {'numeric': isNumeric()});
    });
    
    scope.$watch(isNumeric, function (numeric) {
      updateColumn(getIndex(), {'numeric': numeric});
    });
    
    scope.$watch('orderBy', function (orderBy) {
      if(orderBy) {
        enableSorting();
      } else {
        disableSorting();
      }
    });
  }

  return {
    compile: compile,
    require: ['^^mdHead', '^^mdTable'],
    restrict: 'A',
    scope: {
      numeric: '=?mdNumeric',
      orderBy: '@?mdOrderBy'
    }
  };
}

mdColumn.$inject = ['$compile', '$mdUtil'];

angular.module('md.data.table')
  .decorator('$controller', controllerDecorator)
  .factory('$mdEditDialog', mdEditDialog);

/*
 * A decorator for ng.$controller to optionally bind properties to the
 * controller before invoking the constructor. Stolen from the ngMock.
 *
 * https://docs.angularjs.org/api/ngMock/service/$controller
 */
function controllerDecorator($delegate) {
  return function(expression, locals, later, ident) {
    if(later && typeof later === 'object') {
      var create = $delegate(expression, locals, true, ident);
      angular.extend(create.instance, later);
      return create();
    }
    return $delegate(expression, locals, later, ident);
  };
}

controllerDecorator.$inject = ['$delegate'];
  
function mdEditDialog($compile, $controller, $document, $mdUtil, $q, $rootScope, $templateCache, $templateRequest, $window) {
  /* jshint validthis: true */
  
  var ESCAPE = 27;
  
  var busy = false;
  var body = angular.element($document.prop('body'));
  
  /*
   * bindToController
   * controller
   * controllerAs
   * locals
   * resolve
   * scope
   * targetEvent
   * template
   * templateUrl
   */
  var defaultOptions = {
    clickOutsideToClose: true,
    disableScroll: true,
    escToClose: true,
    focusOnOpen: true
  };
  
  function build(template, options) {
    var scope = $rootScope.$new();
    var element = $compile(template)(scope);
    var backdrop = $mdUtil.createBackdrop(scope, 'md-edit-dialog-backdrop');
    var controller;
    
    if(options.controller) {
      controller = getController(options, scope, {$element: element, $scope: scope});
    } else {
      angular.extend(scope, options.scope);
    }
    
    if(options.disableScroll) {
      disableScroll(element);
    }
    
    body.prepend(backdrop).append(element.addClass('md-whiteframe-1dp'));
    
    positionDialog(element, options.target);
    
    if(options.focusOnOpen) {
      focusOnOpen(element);
    }
    
    if(options.clickOutsideToClose) {
      backdrop.on('click', function () {
        element.remove();
      });
    }
    
    if(options.escToClose) {
      escToClose(element);
    }
    
    element.on('$destroy', function () {
      busy = false;
      backdrop.remove();
    });
    
    return controller;
  }
  
  function disableScroll(element) {
    var restoreScroll = $mdUtil.disableScrollAround(element, body);
    
    element.on('$destroy', function () {
      restoreScroll();
    });
  }
  
  function getController(options, scope, inject) {
    if(!options.controller) {
      return;
    }
    
    if(options.resolve) {
      angular.extend(inject, options.resolve);
    }
    
    if(options.locals) {
      angular.extend(inject, options.locals);
    }
    
    if(options.controllerAs) {
      scope[options.controllerAs] = {};
      
      if(options.bindToController) {
        angular.extend(scope[options.controllerAs], options.scope);
      } else {
        angular.extend(scope, options.scope);
      }
    } else {
      angular.extend(scope, options.scope);
    }
    
    if(options.bindToController) {
      return $controller(options.controller, inject, scope[options.controllerAs]);
    } else {
      return $controller(options.controller, inject);
    }
  }
  
  function getTemplate(options) {
    return $q(function (resolve, reject) {
      var template = options.template;
      
      function illegalType(type) {
        reject('Unexpected template value. Expected a string; received a ' + type + '.');
      }
      
      if(template) {
        return angular.isString(template) ? resolve(template) : illegalType(typeof template);
      }
      
      if(options.templateUrl) {
        template = $templateCache.get(options.templateUrl);
        
        if(template) {
          return resolve(template);
        }
        
        var success = function (template) {
          return resolve(template);
        };
        
        var error = function () {
          return reject('Error retrieving template from URL.');
        };
        
        return $templateRequest(options.templateUrl).then(success, error);
      }
      
      reject('Template not provided.');
    });
  }
  
  function logError(error) {
    busy = false;
    console.error(error); // jshint ignore:line
  }
  
  function escToClose(element) {
    var keyup = function (event) {
      if(event.keyCode === ESCAPE) {
        element.remove();
      }
    };
    
    body.on('keyup', keyup);
    
    element.on('$destroy', function () {
      body.off('keyup', keyup);
    });
  }

  function focusOnOpen(element) {
    $mdUtil.nextTick(function () {
      var autofocus = $mdUtil.findFocusTarget(element);
      
      if(autofocus) {
        autofocus.focus();
      }
    }, false);
  }

  function positionDialog(element, target) {
    var table = angular.element(target).controller('mdCell').getTable();
    
    var getHeight = function () {
      return element.prop('clientHeight');
    };
    
    var getSize = function () {
      return {
        width: getWidth(),
        height: getHeight()
      };
    };
    
    var getTableBounds = function () {
      var parent = table.parent();
      
      if(parent.prop('tagName') === 'MD-TABLE-CONTAINER') {
        return parent[0].getBoundingClientRect();
      } else {
        return table[0].getBoundingClientRect();
      }
    };
    
    var getWidth = function () {
      return element.prop('clientWidth');
    };
    
    var reposition = function () {
      var size = getSize();
      var cellBounds = target.getBoundingClientRect();
      var tableBounds = getTableBounds();
      
      if(size.width > tableBounds.right - cellBounds.left) {
        element.css('left', tableBounds.right - size.width + 'px');
      } else {
        element.css('left', cellBounds.left + 'px');
      }
      
      if(size.height > tableBounds.bottom - cellBounds.top) {
        element.css('top', tableBounds.bottom - size.height + 'px');
      } else {
        element.css('top', cellBounds.top + 1 + 'px');
      }
      
      element.css('minWidth', cellBounds.width + 'px');
    };
    
    var watchWidth = $rootScope.$watch(getWidth, reposition);
    var watchHeight = $rootScope.$watch(getHeight, reposition);
    
    $window.addEventListener('resize', reposition);
    
    element.on('$destroy', function () {
      watchWidth();
      watchHeight();
      
      $window.removeEventListener('resize', reposition);
    });
  }
  
  function preset(size, options) {
    
    function getAttrs() {
      var attrs = 'type="' + (options.type || 'text') + '"';
      
      for(var attr in options.validators) {
        attrs += ' ' + attr + '="' + options.validators[attr] + '"';
      }
      
      return attrs;
    }
    
    return {
      controller: ['$element', '$q', 'save', '$scope', function ($element, $q, save, $scope) {
        function update() {
          if($scope.editDialog.$invalid) {
            return $q.reject();
          }
          
          if(angular.isFunction(save)) {
            return $q.when(save($scope.editDialog.input));
          }
          
          return $q.resolve();
        }
        
        this.dismiss = function () {
          $element.remove();
        };
        
        this.getInput = function () {
          return $scope.editDialog.input;
        };
        
        $scope.dismiss = this.dismiss;
        
        $scope.submit = function () {
          update().then(function () {
            $scope.dismiss();
          });
        };
      }],
      locals: {
        save: options.save
      },
      scope: {
        cancel: options.cancel || 'Cancel',
        messages: options.messages,
        model: options.modelValue,
        ok: options.ok || 'Save',
        placeholder: options.placeholder,
        title: options.title,
        size: size
      },
      template:
        '<md-edit-dialog>' +
          '<div layout="column" class="md-content">' +
            '<div ng-if="size === \'large\'" class="md-title">{{title || \'Edit\'}}</div>' +
            '<form name="editDialog" layout="column" ng-submit="submit(model)">' +
              '<md-input-container md-no-float>' +
                '<input name="input" ng-model="model" md-autofocus placeholder="{{placeholder}} "' + getAttrs() + '>' +
                '<div ng-messages="editDialog.input.$error">' +
                  '<div ng-repeat="(key, message) in messages" ng-message="{{key}}">{{message}}</div>' +
                '</div>' +
              '</md-input-container>' +
            '</form>' +
          '</div>' +
          '<div ng-if="size === \'large\'" layout="row" layout-align="end" class="md-actions">' +
            '<md-button class="md-primary" ng-click="dismiss()">{{cancel}}</md-button>' +
            '<md-button class="md-primary" ng-click="submit()">{{ok}}</md-button>' +
          '</div>' +
        '</md-edit-dialog>'
    };
  }
  
  this.show = function (options) {
    if(busy) {
      return $q.reject();
    }
    
    busy = true;
    options = angular.extend({}, defaultOptions, options);
    
    if(!options.targetEvent) {
      return logError('options.targetEvent is required to align the dialog with the table cell.');
    }
    
    if(!options.targetEvent.currentTarget.classList.contains('md-cell')) {
      return logError('The event target must be a table cell.');
    }
    
    if(options.bindToController && !options.controllerAs) {
      return logError('You must define options.controllerAs when options.bindToController is true.');
    }
    
    options.target = options.targetEvent.currentTarget;
    
    var promise = getTemplate(options);
    var promises = [promise];
    
    for(var prop in options.resolve) {
      promise = options.resolve[prop];
      promises.push($q.when(angular.isFunction(promise) ? promise() : promise));
    }
    
    promise = $q.all(promises);
    
    promise['catch'](logError);
    
    return promise.then(function (results) {
      var template = results.shift();
      
      for(var prop in options.resolve) {
        options.resolve[prop] = results.shift();
      }
      
      return build(template, options);
    });
  };
  
  this.small = function (options) {
    return this.show(angular.extend({}, options, preset('small', options)));
  }.bind(this);
  
  this.large = function (options) {
    return this.show(angular.extend({}, options, preset('large', options)));
  }.bind(this);
  
  return this;
}

mdEditDialog.$inject = ['$compile', '$controller', '$document', '$mdUtil', '$q', '$rootScope', '$templateCache', '$templateRequest', '$window'];


angular.module('md.data.table').directive('mdFoot', mdFoot);

function mdFoot() {

  function compile(tElement) {
    tElement.addClass('md-foot');
  }

  return {
    compile: compile,
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdHead', mdHead);

function mdHead($compile) {

  function compile(tElement) {
    tElement.addClass('md-head');
    return postLink;
  }
  
  // empty controller to be bind scope properties to
  function Controller() {
    
  }
  
  function postLink(scope, element, attrs, tableCtrl) {
    // because scope.$watch is unpredictable
    var oldValue = new Array(2);
    
    function addCheckboxColumn() {
      element.children().prepend('<th class="md-column md-checkbox-column">');
    }
    
    function attatchCheckbox() {
      element.prop('lastElementChild').firstElementChild.appendChild($compile(createCheckBox())(scope)[0]);
    }
    
    function createCheckBox() {
      return angular.element('<md-checkbox>').attr({
        'aria-label': 'Select All',
        'ng-click': 'toggleAll()',
        'ng-checked': 'allSelected()',
        'ng-disabled': '!getSelectableRows().length'
      });
    }
    
    function detachCheckbox() {
      var cell = element.prop('lastElementChild').firstElementChild;
      
      if(cell.classList.contains('md-checkbox-column')) {
        angular.element(cell).empty();
      }
    }
    
    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }
    
    function mdSelectCtrl(row) {
      return angular.element(row).controller('mdSelect');
    }
    
    function removeCheckboxColumn() {
      Array.prototype.some.call(element.find('th'), function (cell) {
        return cell.classList.contains('md-checkbox-column') && cell.remove();
      });
    }
    
    scope.allSelected = function () {
      var rows = scope.getSelectableRows();
      
      return rows.length && rows.every(function (row) {
        return row.isSelected();
      });
    };
    
    scope.getSelectableRows = function () {
      return tableCtrl.getBodyRows().map(mdSelectCtrl).filter(function (ctrl) {
        return ctrl && !ctrl.disabled;
      });
    };
    
    scope.selectAll = function () {
      tableCtrl.getBodyRows().map(mdSelectCtrl).forEach(function (ctrl) {
        if(ctrl && !ctrl.isSelected()) {
          ctrl.select();
        }
      });
    };
    
    scope.toggleAll = function () {
      return scope.allSelected() ? scope.unSelectAll() : scope.selectAll();
    };
    
    scope.unSelectAll = function () {
      tableCtrl.getBodyRows().map(mdSelectCtrl).forEach(function (ctrl) {
        if(ctrl && ctrl.isSelected()) {
          ctrl.deselect();
        }
      });
    };
    
    scope.$watchGroup([enableRowSelection, tableCtrl.enableMultiSelect], function (newValue) {
      if(newValue[0] !== oldValue[0]) {
        if(newValue[0]) {
          addCheckboxColumn();
          
          if(newValue[1]) {
            attatchCheckbox();
          }
        } else {
          removeCheckboxColumn();
        }
      } else if(newValue[0] && newValue[1] !== oldValue[1]) {
        if(newValue[1]) {
          attatchCheckbox();
        } else {
          detachCheckbox();
        }
      }
      
      angular.copy(newValue, oldValue);
    });
  }
  
  return {
    bindToController: true,
    compile: compile,
    controller: Controller,
    controllerAs: '$mdHead',
    require: '^^mdTable',
    restrict: 'A',
    scope: {
      order: '=?mdOrder',
      onReorder: '=?mdOnReorder'
    }
  };
}

mdHead.$inject = ['$compile'];

angular.module('md.data.table').directive('mdRow', mdRow);

function mdRow() {

  function compile(tElement) {
    tElement.addClass('md-row');
    return postLink;
  }
  
  function postLink(scope, element, attrs, tableCtrl) {
    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }
    
    function isBodyRow() {
      return tableCtrl.getBodyRows().indexOf(element[0]) !== -1;
    }
    
    function isChild(node) {
      return element[0].contains(node[0]);
    }
    
    if(isBodyRow()) {
      var cell = angular.element('<td class="md-cell">');
      
      scope.$watch(enableRowSelection, function (enable) {
        // if a row is not selectable, prepend an empty cell to it
        if(enable && !attrs.mdSelect) {
          if(!isChild(cell)) {
            element.prepend(cell);
          }
          return;
        }
        
        if(isChild(cell)) {
          cell.remove();
        }
      });
    }
  }

  return {
    compile: compile,
    require: '^^mdTable',
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdSelect', mdSelect);

function mdSelect($compile, $parse) {

  // empty controller to bind scope properties to
  function Controller() {

  }

  function postLink(scope, element, attrs, ctrls) {
    var self = ctrls.shift();
    var tableCtrl = ctrls.shift();
    var getId = $parse(attrs.mdSelectId);

    self.id = getId(self.model);

    if(tableCtrl.$$rowSelect && self.id) {
      if(tableCtrl.$$hash.has(self.id)) {
        var index = tableCtrl.selected.indexOf(tableCtrl.$$hash.get(self.id));

        // if the item is no longer selected remove it
        if(index === -1) {
          tableCtrl.$$hash.purge(self.id);
        }

        // if the item is not a reference to the current model update the reference
        else if(!tableCtrl.$$hash.equals(self.id, self.model)) {
          tableCtrl.$$hash.update(self.id, self.model);
          tableCtrl.selected.splice(index, 1, self.model);
        }

      } else {

        // check if the item has been selected
        tableCtrl.selected.some(function (item, index) {
          if(getId(item) === self.id) {
            tableCtrl.$$hash.update(self.id, self.model);
            tableCtrl.selected.splice(index, 1, self.model);

            return true;
          }
        });
      }
    }

    self.isSelected = function () {
      if(!tableCtrl.$$rowSelect) {
        return false;
      }

      if(self.id) {
        return tableCtrl.$$hash.has(self.id);
      }

      return tableCtrl.selected.indexOf(self.model) !== -1;
    };

    self.select = function () {
      if(self.disabled) {
        return;
      }

      if(tableCtrl.enableMultiSelect()) {
        tableCtrl.selected.push(self.model);
      } else {
        tableCtrl.selected.splice(0, tableCtrl.selected.length, self.model);
      }

      if(angular.isFunction(self.onSelect)) {
        self.onSelect(self.model);
      }
    };

    self.deselect = function () {
      if(self.disabled) {
        return;
      }

      tableCtrl.selected.splice(tableCtrl.selected.indexOf(self.model), 1);

      if(angular.isFunction(self.onDeselect)) {
        self.onDeselect(self.model);
      }
    };

    self.toggle = function (event) {
      if(event && event.stopPropagation) {
        event.stopPropagation();
      }

      return self.isSelected() ? self.deselect() : self.select();
    };

    function autoSelect() {
      return attrs.mdAutoSelect === '' || self.autoSelect;
    }

    function createCheckbox() {
      var checkbox = angular.element('<md-checkbox>').attr({
        'aria-label': 'Select Row',
        'ng-click': '$mdSelect.toggle($event)',
        'ng-checked': '$mdSelect.isSelected()',
        'ng-disabled': '$mdSelect.disabled'
      });

      return angular.element('<td class="md-cell md-checkbox-cell">').append($compile(checkbox)(scope));
    }

    function disableSelection() {
      Array.prototype.some.call(element.children(), function (child) {
        return child.classList.contains('md-checkbox-cell') && element[0].removeChild(child);
      });

      if(autoSelect()) {
        element.off('click', toggle);
      }
    }

    function enableSelection() {
      element.prepend(createCheckbox());

      if(autoSelect()) {
        element.on('click', toggle);
      }
    }

    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }

    function onSelectChange(selected) {
      if(!self.id) {
        return;
      }

      if(tableCtrl.$$hash.has(self.id)) {
        // check if the item has been deselected
        if(selected.indexOf(tableCtrl.$$hash.get(self.id)) === -1) {
          tableCtrl.$$hash.purge(self.id);
        }

        return;
      }

      // check if the item has been selected
      if(selected.indexOf(self.model) !== -1) {
        tableCtrl.$$hash.update(self.id, self.model);
      }
    }

    function toggle(event) {
      scope.$applyAsync(function () {
        self.toggle(event);
      });
    }

    scope.$watch(enableRowSelection, function (enable) {
      if(enable) {
        enableSelection();
      } else {
        disableSelection();
      }
    });

    scope.$watch(autoSelect, function (newValue, oldValue) {
      if(newValue === oldValue) {
        return;
      }

      if(tableCtrl.$$rowSelect && newValue) {
        element.on('click', toggle);
      } else {
        element.off('click', toggle);
      }
    });

    scope.$watch(self.isSelected, function (isSelected) {
      return isSelected ? element.addClass('md-selected') : element.removeClass('md-selected');
    });

    scope.$watch(tableCtrl.enableMultiSelect, function (multiple) {
      if(tableCtrl.$$rowSelect && !multiple) {
        // remove all but the first selected item
        tableCtrl.selected.splice(1);
      }
    });

    tableCtrl.registerModelChangeListener(onSelectChange);

    element.on('$destroy', function () {
      tableCtrl.removeModelChangeListener(onSelectChange);
    });
  }

  return {
    bindToController: true,
    controller: Controller,
    controllerAs: '$mdSelect',
    link: postLink,
    require: ['mdSelect', '^^mdTable'],
    restrict: 'A',
    scope: {
      model: '=mdSelect',
      disabled: '=ngDisabled',
      onSelect: '=?mdOnSelect',
      onDeselect: '=?mdOnDeselect',
      autoSelect: '=mdAutoSelect'
    }
  };
}

mdSelect.$inject = ['$compile', '$parse'];

angular.module('md.data.table').directive('mdTable', mdTable);

function Hash() {
  var keys = {};
  
  this.equals = function (key, item) {
    return keys[key] === item;
  };

  this.get = function (key) {
    return keys[key];
  };
  
  this.has = function (key) {
    return keys.hasOwnProperty(key);
  };

  this.purge = function (key) {
    delete keys[key];
  };
  
  this.update = function (key, item) {
    keys[key] = item;
  };
}

function mdTable() {
  
  function compile(tElement, tAttrs) {
    tElement.addClass('md-table');
    
    if(tAttrs.hasOwnProperty('mdProgress')) {
      var body = tElement.find('tbody')[0];
      var progress = angular.element('<thead class="md-table-progress">');
      
      if(body) {
        tElement[0].insertBefore(progress[0], body);
      }
    }
  }
  
  function Controller($attrs, $element, $q, $scope) {
    var self = this;
    var queue = [];
    var watchListener;
    var modelChangeListeners = [];
    
    self.$$hash = new Hash();
    self.$$columns = {};
    
    function enableRowSelection() {
      self.$$rowSelect = true;
      
      watchListener = $scope.$watchCollection('$mdTable.selected', function (selected) {
        modelChangeListeners.forEach(function (listener) {
          listener(selected);
        });
      });
      
      $element.addClass('md-row-select');
    }
    
    function disableRowSelection() {
      self.$$rowSelect = false;
      
      if(angular.isFunction(watchListener)) {
        watchListener();
      }
      
      $element.removeClass('md-row-select');
    }
    
    function resolvePromises() {
      if(!queue.length) {
        return $scope.$applyAsync();
      }
      
      queue[0]['finally'](function () {
        queue.shift();
        resolvePromises();
      });
    }
    
    function rowSelect() {
      return $attrs.mdRowSelect === '' || self.rowSelect;
    }
    
    function validateModel() {
      if(!self.selected) {
        return console.error('Row selection: ngModel is not defined.'); // jshint ignore:line
      }
      
      if(!angular.isArray(self.selected)) {
        return console.error('Row selection: Expected an array. Recived ' + typeof self.selected + '.'); // jshint ignore:line
      }
      
      return true;
    }
    
    self.columnCount = function () {
      return self.getRows($element[0]).reduce(function (count, row) {
        return row.cells.length > count ? row.cells.length : count;
      }, 0);
    };
    
    self.getRows = function (element) {
      return Array.prototype.filter.call(element.rows, function (row) {
        return !row.classList.contains('ng-leave');
      });
    };
    
    self.getBodyRows = function () {
      return Array.prototype.reduce.call($element.prop('tBodies'), function (result, tbody) {
        return result.concat(self.getRows(tbody));
      }, []);
    };
    
    self.getElement = function () {
      return $element;
    };
    
    self.getHeaderRows = function () {
      return self.getRows($element.prop('tHead'));
    };
    
    self.enableMultiSelect = function () {
      return $attrs.multiple === '' || $scope.$eval($attrs.multiple);
    };
    
    self.waitingOnPromise = function () {
      return !!queue.length;
    };
    
    self.queuePromise = function (promise) {
      if(!promise) {
        return;
      }
      
      if(queue.push(angular.isArray(promise) ? $q.all(promise) : $q.when(promise)) === 1) {
        resolvePromises();
      }
    };
    
    self.registerModelChangeListener = function (listener) {
      modelChangeListeners.push(listener);
    };
    
    self.removeModelChangeListener = function (listener) {
      var index = modelChangeListeners.indexOf(listener);
      
      if(index !== -1) {
        modelChangeListeners.splice(index, 1);
      }
    };
    
    if($attrs.hasOwnProperty('mdProgress')) {
      $scope.$watch('$mdTable.progress', self.queuePromise);
    }
    
    $scope.$watch(rowSelect, function (enable) {
      if(enable && !!validateModel()) {
        enableRowSelection();
      } else {
        disableRowSelection();
      }
    });
  }
  
  Controller.$inject = ['$attrs', '$element', '$q', '$scope'];
  
  return {
    bindToController: true,
    compile: compile,
    controller: Controller,
    controllerAs: '$mdTable',
    restrict: 'A',
    scope: {
      progress: '=?mdProgress',
      selected: '=ngModel',
      rowSelect: '=mdRowSelect'
    }
  };
}

angular.module('md.data.table').directive('mdTablePagination', mdTablePagination);

function mdTablePagination() {

  function compile(tElement) {
    tElement.addClass('md-table-pagination');
  }

  function Controller($attrs, $mdUtil, $scope) {
    var self = this;
    var defaultLabel = {
      page: 'Página:',
      rowsPerPage: 'Linhas por página:',
      of: 'de'
    };

    self.label = angular.copy(defaultLabel);

    function isPositive(number) {
      return parseInt(number, 10) > 0;
    }

    self.first = function () {
      self.page = 1;
      self.onPaginationChange();
    };

    self.hasNext = function () {
      return self.page * self.limit < self.total;
    };

    self.hasPrevious = function () {
      return self.page > 1;
    };

    self.last = function () {
      self.page = self.pages();
      self.onPaginationChange();
    };

    self.max = function () {
      return self.hasNext() ? self.page * self.limit : self.total;
    };

    self.min = function () {
      return isPositive(self.total) ? self.page * self.limit - self.limit + 1 : 0;
    };

    self.next = function () {
      self.page++;
      self.onPaginationChange();
    };

    self.onPaginationChange = function () {
      if(angular.isFunction(self.onPaginate)) {
        $mdUtil.nextTick(function () {
          self.onPaginate(self.page, self.limit);
        });
      }
    };

    self.pages = function () {
      return isPositive(self.total) ? Math.ceil(self.total / (isPositive(self.limit) ? self.limit : 1)) : 1;
    };

    self.previous = function () {
      self.page--;
      self.onPaginationChange();
    };

    self.showBoundaryLinks = function () {
      return $attrs.mdBoundaryLinks === '' || self.boundaryLinks;
    };

    self.showPageSelect = function () {
      return $attrs.mdPageSelect === '' || self.pageSelect;
    };

    $scope.$watch('$pagination.limit', function (newValue, oldValue) {
      if(isNaN(newValue) || isNaN(oldValue) || newValue === oldValue) {
        return;
      }

      // find closest page from previous min
      self.page = Math.floor(((self.page * oldValue - oldValue) + newValue) / (isPositive(newValue) ? newValue : 1));
      self.onPaginationChange();
    });

    $attrs.$observe('mdLabel', function (label) {
      angular.extend(self.label, defaultLabel, $scope.$eval(label));
    });

    $scope.$watch('$pagination.total', function (newValue, oldValue) {
      if(isNaN(newValue) || newValue === oldValue) {
        return;
      }

      if(self.page > self.pages()) {
        self.last();
      }
    });
  }

  Controller.$inject = ['$attrs', '$mdUtil', '$scope'];

  return {
    bindToController: {
      boundaryLinks: '=?mdBoundaryLinks',
      disabled: '=ngDisabled',
      limit: '=mdLimit',
      page: '=mdPage',
      pageSelect: '=?mdPageSelect',
      onPaginate: '=?mdOnPaginate',
      limitOptions: '=?mdLimitOptions',
      total: '@mdTotal'
    },
    compile: compile,
    controller: Controller,
    controllerAs: '$pagination',
    restrict: 'E',
    scope: {},
    templateUrl: 'md-table-pagination.html'
  };
}

angular.module('md.data.table').directive('mdTableProgress', mdTableProgress);

function mdTableProgress() {

  function postLink(scope, element, attrs, tableCtrl) {
    scope.columnCount = tableCtrl.columnCount;
    scope.deferred = tableCtrl.waitingOnPromise;
  }

  return {
    link: postLink,
    require: '^^mdTable',
    restrict: 'C',
    scope: {},
    templateUrl: 'md-table-progress.html'
  };
}

angular.module('md.data.table').directive('virtualPageSelect', virtualPageSelect);

function virtualPageSelect() {

  function Controller($element, $scope) {
    var self = this;
    var content = $element.find('md-content');
    
    self.pages = [];
    
    function getMin(pages, total) {
      return Math.min(pages, isFinite(total) && isPositive(total) ? total : 1);
    }
    
    function isPositive(number) {
      return number > 0;
    }
    
    function setPages(max) {
      if(self.pages.length > max) {
        return self.pages.splice(max);
      }
      
      for(var i = self.pages.length; i < max; i++) {
        self.pages.push(i + 1);
      }
    }
    
    content.on('scroll', function () {
      if((content.prop('clientHeight') + content.prop('scrollTop')) >= content.prop('scrollHeight')) {
        $scope.$applyAsync(function () {
          setPages(getMin(self.pages.length + 10, self.total));
        });
      }
    });
    
    $scope.$watch('$pageSelect.total', function (total) {
      setPages(getMin(Math.max(self.pages.length, 10), total));
    });
  }

  Controller.$inject = ['$element', '$scope'];

  return {
    bindToController: {
      total: '@'
    },
    controller: Controller,
    controllerAs: '$pageSelect'
  };
}

})(window, angular);
(function () {
    'use strict';

    angular
        .module('su-componentes')
        .directive('dadosPessoa',directive);

    function directive ($mdDialog) {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-componentes/infoconv/dadosPessoa.html',
            scope: {
                responsavel: '=',
                historicoPessoaFisica: '=',
                destinacaoPorResponsavel: '=',
                documentoPessoaFisica: '='
            },
            link: function ($scope) {

                var vm = $scope;

                var init = function() {
                    criarLista();
                };

                var criarLista = function() {
                    angular.forEach(vm.historicoPessoaFisica, function (historico) {
                        if(vm.responsavel.cpf === historico.cpf[0].valor) {
                            vm.historicoPessoaFisica = historico;
                        }
                    });
                };

                vm.fechar = function() {
                    window.close();
                };

                vm.historico = function(ev, pessoa) {
                    $mdDialog.show({
                        controller: function ($scope) {

                            $scope.historico = pessoa;

                            var init = function() {
                                angular.forEach($scope.historico, function (historico) {
                                    historico.data = new Date(historico.data);
                                });
                            };

                            init();

                            $scope.fechar = function() {
                                $mdDialog.cancel();
                            };
                        },
                        templateUrl: 'scripts-componentes/infoconv/componentes/dadosPessoais/modal/modalHistorico.html',
                        targetEvent: ev,
                        locals: {

                        }
                    });
                };

                init();
            }
        };
    }


})();

(function () {
    'use strict';

    angular
        .module('su-componentes')
        .directive('dadosPessoaJuridica',directive);

    function directive ($mdDialog) {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-componentes/infoconv/dadosPessoaJuridica.html',
            scope: {
                responsavel: '=',
                historicoPessoaJuridica: '='
            },
            link: function ($scope) {

                var vm = $scope;

                var init = function() {
                    criarLista();
                };

                var criarLista = function() {
                    angular.forEach(vm.historicoPessoaJuridica, function (historico) {
                        if(vm.responsavel.cnpj === historico.cnpj[0].valor) {
                            vm.historicoPessoaJuridica = historico;
                        }
                    });
                };

                vm.fechar = function() {
                    window.close();
                };

                vm.historico = function(ev, pessoa) {
                    $mdDialog.show({
                        controller: function ($scope) {

                            $scope.historico = pessoa;

                            var init = function() {
                                angular.forEach($scope.historico, function (historico) {
                                    historico.data = new Date(historico.data);
                                });
                            };

                            init();

                            $scope.fechar = function() {
                                $mdDialog.cancel();
                            };
                        },
                        templateUrl: 'scripts-componentes/infoconv/componentes/dadosEmpresariais/modal/modalHistorico.html',
                        targetEvent: ev,
                        locals: {

                        }
                    });
                };

                init();
            }
        };
    }


})();

(function () {
    'use strict';
    angular.module('su-componentes')
        .factory('errorHandlerInterceptor', function ($q, $rootScope, $window, urlPrefixo) {
            return {
                'responseError': function (response) {
                    if (response.status == 403 && response.data.path.startsWith(urlPrefixo)) {
                        $window.location.href = urlPrefixo + '/api';
                    }
                    return $q.reject(response);
                }
            };
        });
})();

(function () {
    'use strict';
    angular.module('su-componentes')
        .controller('MenuController', function ($mdSidenav) {
            var self = this;

            var init = function () {
                angular.element('.md-menu-bar-menu').removeClass('md-dense');
            };

            init();

            self.toggleMenu = function () {
                $mdSidenav('left').toggle();
            };
            self.closeMenu = function () {
                $mdSidenav('left').close();
            };
        });
})();

(function () {
    'use strict';

    angular
        .module('su-componentes')
        .directive('menu', ['$mdSidenav', '$http', '$rootScope', 'usuarioService', '$log',
                            'urlPrefixo', '$state', 'escopoCompartilhadoService', directive]);

    function directive($mdSidenav, $http, $rootScope, usuarioService, $log, urlPrefixo,
                       $state, escopoCompartilhadoService) {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-componentes/menu/menu.html',
            link: function ($scope, $element, $attrs) {
                var prefixo = urlPrefixo + '/api';
                var urlUsuario = prefixo + '/cliente/usuario-logado';
                var urlSiapa = '#';

                $scope.getMenuAcessos = function(){
                  return escopoCompartilhadoService.getObjeto('menu-acessos');
                };

                // recomputes only when menu-acessos data changes, avoiding infinite digest
                $scope.$watch(function() {
                    return escopoCompartilhadoService.getObjeto('menu-acessos');
                }, function(todos) {
                    var codigos = $attrs.codigoModulo ? $attrs.codigoModulo.split(',') : [];
                    if (!codigos.length || !todos) {
                        $scope.menuAcessosFiltrado = todos;
                        return;
                    }

                    var filtrado = [];
                    angular.forEach(todos, function(item) {
                        var paisFiltrados = [];
                        angular.forEach(item.nos, function(subItemPai) {
                            var filhosFiltrados = (subItemPai.nos || []).filter(function(filho) {
                                return codigos.indexOf(filho.codigoModulo) !== -1;
                            });
                            if (filhosFiltrados.length > 0) {
                                var paiCopy = angular.copy(subItemPai);
                                paiCopy.nos = filhosFiltrados;
                                paisFiltrados.push(paiCopy);
                            }
                        });
                        if (paisFiltrados.length > 0) {
                            var itemCopy = angular.copy(item);
                            itemCopy.nos = paisFiltrados;
                            filtrado.push(itemCopy);
                        }
                    });
                    $scope.menuAcessosFiltrado = filtrado;
                });

                // kept for backwards compatibility (used by directives without codigo-modulo)
                $scope.getMenuAcessosFiltrado = function() {
                    return $scope.menuAcessosFiltrado;
                };
                
                $scope.toggleMenu = function () {
                    $mdSidenav('left').toggle();
                };

                $scope.closeMenu = function () {
                    $mdSidenav('left').close();
                };

                $scope.clickMenuSiapa = function(item) {
                    if (item.url) {
                        $scope.closeMenu();
                        $state.go('INTEGRADOR.SIAPA',{urlMenu:$scope.getUrl(item)});
                        return false;
                    }
                };

                $scope.isMenuSiapa= function(item) {
                    return item.codigoModulo == 'SU.SIAPA';
                };

              /**
               * Permite criar URLs dinâmicas com o módulo e os itens
               * filhos criando a URL do sistema externo e permitindo
               * navegação direta entre os sistemas da SPU
               * @param {*} subItemPai 
               * @param {*} subItemFilho 
               * @returns 
               */
              $scope.clickMenuSistemaExterno = function(subItemPai, subItemFilho) {
                var urlSistemaDestino = subItemPai.urlModulo;
                if (urlSistemaDestino) {
                    $scope.closeMenu();
                    if(urlSistemaDestino.slice(-1) === '/') {
                      urlSistemaDestino = urlSistemaDestino.substring(0, urlSistemaDestino.length - 1);
                    }
                    
                    var urlFinalDestino = urlSistemaDestino;
                    if(subItemFilho.url) {
                      urlFinalDestino = urlFinalDestino + subItemFilho.url;
                    } else {
                        if(subItemFilho.nos){
                            urlFinalDestino = urlFinalDestino + subItemFilho.nos[0].url;
                        }
                    }

                    window.open(urlFinalDestino, '+blank');
                    return false;
                }
            };
              /**
               * Adicione o sistema abaixo recuperado do BD para o menu
               * aceitar links diretos com sistemas externos
               * @param {*} item 
               * @returns 
               */
              $scope.isMenuSistemaExterno= function(item) {
                  return item.codigoModulo == 'SU.GCC'
                   || item.codigoModulo == 'SU.DESTINACAO';
              };

                var buscarUrlsMenusPorAmbiente = function () {
                    $http.get(prefixo+'/comum/url-menu').then(function (response) {
                        if(angular.isDefined(response.data.resposta) 
                                && response.data.resposta.length > 0 ){
                            urlSiapa = response.data.resposta[0].siapa;
                        }
                    });
                };

                $scope.getUrl = function (item) {
                    if ($scope.isMenuSiapa(item)) {
                        return urlSiapa + item.url;
                    } else {
                        return '#' + item.url;
                    }
                };

                var carregarDadosUsuarioLogado = function () {
                    $http.get(urlUsuario).then(function (response) {
                        $rootScope.usuarioLogado = response.data;

                        if(sessionStorage.getItem('menu-acessos') !== null){
                            console.log('Menu recuperado...');
                            escopoCompartilhadoService.setObjetos('menu-acessos', 
                                JSON.parse(sessionStorage.getItem('menu-acessos')));
                         }else{
                            usuarioService.getMenuUsuario($rootScope.usuarioLogado);
                         }
                    }, function (error) {
                        $log.error('ERRO AO BUSCAR USUARIO LOGADO', error);
                    });
                };

                function init() {
                    buscarUrlsMenusPorAmbiente();
                    carregarDadosUsuarioLogado();
                }

                init();
            }
        };
    }
}());

(function () {
    'use strict';
    angular.module('su-componentes').directive('exibirElementoPorPermissao', function ($rootScope) {
        return ({
            restrict: 'A',
            link: function link($scope, element, $attr) {
                angular.element(element).addClass('invisivel');
                var verificarPermissao = function () {
                    var roles = ($attr.exibirElementoPorPermissao || '').split(',');
                    var usuarioLogado = $rootScope.usuarioLogado;
                    var possuiPermissoes = usuarioLogado && usuarioLogado.permissoes;
                    for (var i = 0; i < roles.length && possuiPermissoes; i++) {
                        if (usuarioLogado.permissoes.indexOf(roles[i].trim()) != -1) {
                             angular.element(element).removeClass('invisivel');
                            return;
                        }
                    }
                    angular.element(element).remove();
                };
                $scope.$watch('usuarioLogado', function (values) {
                    if (values || angular.isDefined($rootScope.usuarioLogado)) {
                        verificarPermissao();
                    }
                });
            }
        });
    });
})();
(function () {
    'use strict';
    angular.module('su-componentes')
        .component('rodape', {
            templateUrl: 'scripts-componentes/rodape/rodape.html'
        });
})();

(function () {

  'use strict';

  angular.module('su-componentes')
    .directive('tituloServico', directive);

  function directive($location, $rootScope, escopoCompartilhadoService) {
    return {
      restrict: 'EA',
      scope: {
        dinamico: '=',
        tituloStatico: '='
      },
      templateUrl: 'scripts-componentes/tituloServico/tituloServico.html',
      link: function ($scope) {

        var getMenuAcessos = function(){
            return escopoCompartilhadoService.getObjeto('menu-acessos');
        };

        $scope.buscarTitulo = function () {
          
          if(angular.isUndefined($scope.titulo)){

              angular.forEach(getMenuAcessos(), function (item) {
                angular.forEach(item.nos, function (subitem) {
                  angular.forEach(subitem.nos, function (no) {
                    var path = $location.path();
                    if (no.url === path) {
                      $scope.titulo = no.descricaoResumida;
                    }
                  });
                });
              });
          }

          return $scope.titulo;
        };

      }
    };
  }

})();

/**
 * Created by Joyce Cam on 30/12/2014.
 *
 * Simple and easy-to-implement angular read more directive.
 * https://github.com/pattysnippets/angular-readmore
 */

(function () {

    'use strict';

    var readMore = angular.module('readMore', []);

    readMore.directive('readMore', function () {
        return {
            restrict: 'A',
            transclude: true,
            replace: true,
            template: '<p></p>',
            scope: {
                moreText: '@',
                lessText: '@',
                words: '@',
                ellipsis: '@',
                char: '@',
                limit: '@',
                content: '@'
            },
            link: function (scope, elem, attr, ctrl, transclude) {
                var moreText = angular.isUndefined(scope.moreText) ? ' <a class="read-more">Read More...</a>' : ' <a class="read-more">' + scope.moreText + '</a>',
                    lessText = angular.isUndefined(scope.lessText) ? ' <a class="read-less">Less ^</a>' : ' <a class="read-less">' + scope.lessText + '</a>',
                    ellipsis = angular.isUndefined(scope.ellipsis) ? '' : scope.ellipsis,
                    limit = angular.isUndefined(scope.limit) ? 150 : scope.limit;

                attr.$observe('content', function (str) {
                    readmore(str);
                });

                transclude(scope.$parent, function (clone) {
                    readmore(clone.text().trim());
                });

                function readmore(tex) {

                    var text = tex,
                        orig = tex,
                        regex = /\s+/gi,
                        charCount = text.length,
                        wordCount = text.trim().replace(regex, ' ').split(' ').length,
                        countBy = 'char',
                        count = charCount,
                        foundWords = [],
                        markup = text,
                        more = '';

                    if (!angular.isUndefined(attr.words)) {
                        countBy = 'words';
                        count = wordCount;
                    }

                    if (countBy === 'words') { // Count words

                        foundWords = text.split(/\s+/);

                        if (foundWords.length > limit) {
                            text = foundWords.slice(0, limit).join(' ') + ellipsis;
                            more = foundWords.slice(limit, count).join(' ');
                            markup = text + moreText + '<span class="more-text">' + more + lessText + '</span>';
                        }

                    } else { // Count characters

                        if (count > limit) {
                            text = orig.slice(0, limit) + ellipsis;
                            more = orig.slice(limit, count);
                            markup = text + moreText + '<span class="more-text">' + more + lessText + '</span>';
                        }

                    }

                    elem.append(markup);
                    elem.find('.read-more').on('click', function () {
                        $(this).hide();
                        elem.find('.more-text').addClass('show').slideDown();
                    });
                    elem.find('.read-less').on('click', function () {
                        elem.find('.read-more').show();
                        elem.find('.more-text').hide().removeClass('show');
                    });

                }
            }
        };
    });
})();
(function () {

    'use strict';
    angular
        .module('su-componentes')
        .factory('ComumFactory', ['$http', '$mdToast', function ($http, $mdToast) {

            /**
             * Passar uma string ou uma array de strings para várias mensagens
             * @param mensagens
             */
            var mostrarMensagemError = function (mensagens) {
                $mdToast.show({
                    controller: toastController(mensagens),
                    templateUrl: 'scripts-componentes/toast/toast-template-error.html',
                    focusOnOpen: false,
                    hideDelay: 30000,
                    position: 'top right'
                });
            };

            var exibirMesagemErroVariosCampos = function (titulo, mensagens) {
                $mdToast.show({
                    controller: toastController(mensagens, titulo),
                    templateUrl: 'scripts-componentes/toast/toast-template-error.html',
                    focusOnOpen: false,
                    hideDelay: 30000,
                    position: 'top right'
                });
            };

            var mostrarMensagemSucesso = function (mensagens) {
                mensagens = (typeof mensagens == 'string') ? [mensagens] : mensagens;
                mensagens.forEach(function (msg) {
                    $mdToast.show({
                        controller: toastController(msg),
                        templateUrl: 'scripts-componentes/toast/toast-template-success.html',
                        focusOnOpen: false,
                        hideDelay: 30000,
                        position: 'top right'
                    });
                });
            };

            var toastController = function (mensagens, titulo) {
                return ['$scope', '$mdToast', function ($scope, $mdToast) {

                    $scope.show = false;
                    $scope.titulo = titulo;
                    $scope.msgs = mensagens;
                    $scope.isArray = $scope.msgs instanceof Array;

                    $scope.$on('toast-close', function () {
                        $scope.show = false;
                        $mdToast.hide();
                    });

                    function showToast() {
                        if (!$scope.show) {
                            $scope.show = true;
                        } else {
                            $scope.$broadcast('toast-close');
                        }
                    }

                    function cleanUp() {
                        angular.element(window).off('click', showToast);
                    }

                    angular.element(window).on('click', showToast);
                    $scope.$on('$destroy', cleanUp);
                }];
            };

            var removerHTML = function (texto) {
                texto = texto ? String(texto).replace(/<[^>]+>/gm, ' ') : '';
                texto = texto.replace(/&nbsp;/g, '\n')
                    .replace(/\n/g, '');
                return texto;
            };

            var exibirRepresentanteLegal = function(requerimento) {
                if (requerimento) {
                    return requerimento.requerente && requerimento.requerente.representanteLegal;
                }
                return true;
            };

            return {
                mostrarMensagemError: mostrarMensagemError,
                mostrarMensagemSucesso: mostrarMensagemSucesso,
                exibirMesagemErroVariosCampos: exibirMesagemErroVariosCampos,
                removerHTML: removerHTML,
                exibirRepresentanteLegal: exibirRepresentanteLegal
            };

        }]);
})();

(function () {
    'use strict';
    angular
        .module('su-componentes')
        .factory('escopoCompartilhadoService', ['$state', service]);
    function service ($state) {
        var _objetos = {};
        var setObjetos = function (nomeObjeto, objeto) {
            _objetos[nomeObjeto] = objeto;
        };
        var getObjeto = function (nomeObjeto) {
            return _objetos[nomeObjeto];
        };
        var getObjetoDefined = function(nomeObjeto, rota){
            var obj = getObjeto(nomeObjeto);
            if(angular.isUndefined(obj)){
                $state.transitionTo(rota);
            }else{
                return obj;
            }
        };
        var limparEscopo = function(){
            _objetos = {};
        };
        return {
            setObjetos: setObjetos,
            getObjeto: getObjeto,
            limparEscopo: limparEscopo,
            getObjetoDefined: getObjetoDefined
        };
    }
})();
(function(){

    'use strict';

    angular
        .module('su-componentes')
        .service('FormularioFactory', FormularioFactory);

    function FormularioFactory($filter, ComumFactory) {

        var validarFormularioComMensagem = function(form){

            var error = form.$error;
            var invalid = true;

            angular.forEach(error.required, function (field) {
                if(field.$invalid){
                    ComumFactory.mostrarMensagemError($filter('translate')('erro-campos-obrigatorio'));
                    invalid = false;
                }
            });

            return invalid;
        };


        return {
            validarFormularioComMensagem: validarFormularioComMensagem
        };
    }

})();

/**
 * Created by gabriel on 29/08/16.
 */

(function () {
    'use strict';

    angular.module('su-componentes')
        .factory('Traducoes', factory);

    factory.$inject = ['$translatePartialLoader', '$translate'];

    function factory($translatePartialLoader, $translate) {
        return function () {
            angular.forEach(arguments, function (translationKey) {
                $translatePartialLoader.addPart(translationKey);
            });
            return $translate.refresh();
        };
    }
})();

/**
 * Created by Basis Tecnologia on 24/08/2016.
 */
(function () {

    'use strict';

    angular
        .module('su-componentes')
        .factory('usuarioService', ['$http', '$rootScope', 'urlPrefixo', 'escopoCompartilhadoService', service]);

    function service($http, $rootScope, urlPrefixo, escopoCompartilhadoService) {

        var getMenuUsuario = function (usuario) {

            $rootScope.usuarioLogado = angular.copy(usuario);

            $http.get(urlPrefixo + '/api/cliente/menu').then(function (retorno) {
                escopoCompartilhadoService.setObjetos('menu-acessos', retorno.data);
                sessionStorage.setItem('menu-acessos', JSON.stringify(retorno.data));
            });
        };

        return {
            getMenuUsuario: getMenuUsuario
        };
    }

}());

(function () {
    'use strict';

    angular
        .module('su-componentes')
        .directive('dadosDeContato',directive);

    function directive () {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-componentes/infoconv/componentes/dadosDeContato/templates/dadosDeContato.html',
            scope: {
                responsavel:'=',
                historicoPessoaFisica: '=',
                funcaoHistorico: '&'
            },

            link: function () {

            }
        };
    }


})();

(function () {
    'use strict';

    angular
        .module('su-componentes')
        .directive('dadosEmpresariais',directive);

    function directive () {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-componentes/infoconv/componentes/dadosEmpresariais/templates/dadosEmpresariais.html',
            scope: {
                responsavel: '=',
                historicoPessoaFisica: '=',
                funcaoHistorico: '&'
            },
            link: function ($scope) {

                $scope.tabelaDocumentos = {
                    limit: 5,
                    limitsPage: [5, 10, 15],
                    page: 1
                };

                $scope.tabelaVinculacaoPessoaJuridica = {
                    limit: 5,
                    limitsPage:[5,10,15],
                    page: 1
                };
            }
        };
    }


})();

(function () {
    'use strict';

    angular
        .module('su-componentes')
        .directive('dadosPessoais',directive);

    function directive () {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-componentes/infoconv/componentes/dadosPessoais/templates/dadosPessoais.html',
            scope: {
                responsavel: '=',
                historicoPessoaFisica: '=',
                funcaoHistorico: '&',
                documentoPessoaFisica:'='
            },
            link: function ($scope) {

                $scope.tabelaDocumentos = {
                    limit: 5,
                    limitsPage: [5, 10, 15],
                    page: 1
                };

                $scope.tabelaVinculacaoPessoaJuridica = {
                    limit: 5,
                    limitsPage:[5,10,15],
                    page: 1
                };
            }
        };
    }


})();

(function () {
    'use strict';

    angular
        .module('su-componentes')
        .directive('dadosProfissionais',directive);

    function directive () {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-componentes/infoconv/componentes/dadosProfissionais/templates/dadosProfissionais.html',
            scope: {

            },
            link: function () {

            }
        };
    }


})();

(function () {
    'use strict';

    angular
        .module('su-componentes')
        .directive('relacionamentoSpu',directive);

    function directive () {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-componentes/infoconv/componentes/relacionamentoSpu/templates/relacionamentoSpu.html',
            scope: {
                destinacaoPorResponsavel: '='
            },
            link: function ($scope) {

                $scope.lpad = function (parcela) {
                    var texto = parcela.substring(1, parcela.length);
                    var digito = parcela.substring(0, 1);
                    return digito + (texto.length >= 4 ? texto : new Array(4 - texto.length + 1).join('0') + texto);
                };

                $scope.tabelaDestinacao = {
                    limit: 5,
                    limitsPage:[5,10,15],
                    page: 1
                };

            }
        };
    }


})();
(function () {
    'use strict';

    angular
        .module('su-componentes')
        .directive('socios',directive);

    function directive (destinacaoService, mensagemDestinacaoService, $state) {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-componentes/infoconv/componentes/socios/templates/socios.html',
            $scope: {
                responsavel: '='
            },
            link: function ($scope) {

                $scope.tabelaSocios = {
                    limit: 5,
                    limitsPage: [5, 10, 15],
                    page: 1,
                    total: 5
                };

                $scope.paginacaoSocios = function() {
                    $scope.sociosPaginados = [];
                    if($scope.responsavel.socios){
                        $scope.tabelaSocios.total = $scope.responsavel.socios.length;

                        if ($scope.tabelaSocios.total < $scope.tabelaSocios.limit) {
                            $scope.sociosPaginados = $scope.responsavel.socios;
                        } else {
                            for (var i = (($scope.tabelaSocios.page - 1) * $scope.tabelaSocios.limit); i < $scope.tabelaSocios.total; i++) {
                                $scope.sociosPaginados.push($scope.responsavel.socios[i]);
                                if ($scope.sociosPaginados.length == $scope.tabelaSocios.limit) {
                                    break;
                                }
                            }
                        }
                    }
                };

                $scope.detalharPessoa = function(cpfCnpj) {
                    var responsavel = null;
                    var historico = [];
                    if(cpfCnpj.length === 11) {
                        destinacaoService.buscarPessoaFisica($scope.usuarioLogado.cpf, cpfCnpj, 'SU-DESTINACAO', 8760).then(function (retorno) {
                            if(retorno.data.erros && retorno.data.erros.length > 0){
                                mensagemDestinacaoService.mostrarMensagemError(retorno.data.erros[0]);
                            } else {
                                responsavel = retorno.data.resultado;
                                window.sessionStorage.setItem('dadoPessoaFisica', JSON.stringify(responsavel));
                                
                                var campo = ['cpf','situacaoCadastral', 'nome', 'dataNascimento', 'anoObito', 'nomeMae', 'sexo', 'estrangeiro', 'tituloEleitor', 'endereco'];
                                
                                var obj = {};
                                var cont = 0, i = 0;
                                angular.forEach(campo, function (campo) {
                                    destinacaoService.buscarHistoricoPessoaFisica(campo, cpfCnpj).then(function (retorno) {
                                        if(retorno.data.erros && retorno.data.erros.length > 0){
                                            mensagemDestinacaoService.mostrarMensagemError(retorno.data.erros[0]);
                                        } else {
                                            obj[campo] = retorno.data.resultado;
                                            if (i==9) {
                                                historico[cont] = obj;
                                                cont++;
                                                obj = {};
                                                window.sessionStorage.setItem('historicoPessoaFisica', JSON.stringify(historico));
                                                var rota = $state.href('destinacao.dadosPessoas');
                                                window.open(rota, '_blank');
                                            }
                                        }
                                        i++;
                                    });
                                });
                            }
                        });
                      } /*else {
                        // window.sessionStorage.setItem('dadoPessoaJuridica', JSON.stringify(responsavel));
                        // window.sessionStorage.setItem('historicoPessoaJuridica', JSON.stringify(historico));
                        // var rota = $state.href('destinacao.dadosPessoasJuridica');
                        // window.open(rota, '_blank');
                      }*/
                };

                $scope.paginacaoSocios();
            }
        };
    }


})();

(function () { 'use strict'; angular.module('su-componentes').run(['$templateCache', function($templateCache) {$templateCache.put('scripts-componentes/diretivas/mapa.html','<div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults width=100% height=100%><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control></openlayers></div><span ng-init=init()></span>');
$templateCache.put('scripts-componentes/infoconv/dadosPessoa.html','<div><md-content class=md-padding flex=100 flex-xs=100><md-tabs md-dynamic-height md-no-ink><md-tab id=dadosPessoais><md-tab-label>{{\'Dados Pessoais\' | uppercase}}</md-tab-label><md-tab-body><div ng-cloak flex=100 flex-xs=100 class=margin-conteudo offset=5><dados-pessoais responsavel=responsavel historico-pessoa-fisica=historicoPessoaFisica funcao-historico=\"historico(ev, pessoa)\" documento-pessoa-fisica=documentoPessoaFisica></dados-pessoais></div></md-tab-body></md-tab><md-tab id=dadosContato><md-tab-label>{{\'Dados De Contato\' | uppercase}}</md-tab-label><md-tab-body><div ng-cloak flex=100 flex-xs=100 class=margin-conteudo offset=5><dados-de-contato responsavel=responsavel historico-pessoa-fisica=historicoPessoaFisica funcao-historico=\"historico(ev, pessoa)\"></dados-de-contato></div></md-tab-body></md-tab><md-tab id=dadosProfissionais><md-tab-label>{{\'Dados Profissionais\' | uppercase}}</md-tab-label><md-tab-body><div ng-cloak flex=100 flex-xs=100 class=margin-conteudo offset=5><dados-profissionais></dados-profissionais></div></md-tab-body></md-tab><md-tab id=relacionamentoSpu><md-tab-label>{{\'Relacionamento SPU\' | uppercase}}</md-tab-label><md-tab-body><div ng-cloak flex=100 flex-xs=100 class=margin-conteudo offset=5><relacionamento-spu destinacao-por-responsavel=destinacaoPorResponsavel></relacionamento-spu></div></md-tab-body></md-tab></md-tabs></md-content><div layout=row layout-align=\"end center\"><md-button class=md-raised ng-click=fechar()>{{\'Fechar\' | uppercase}}</md-button></div></div>');
$templateCache.put('scripts-componentes/infoconv/dadosPessoaJuridica.html','<div><md-content class=md-padding flex=100 flex-xs=100><md-tabs md-dynamic-height md-no-ink><md-tab id=dadosEmpresariais><md-tab-label>{{\'Dados Empresais\' | uppercase}}</md-tab-label><md-tab-body><div ng-cloak flex=100 flex-xs=100 class=margin-conteudo offset=5><dados-empresariais responsavel=responsavel historico-pessoa-fisica=historicoPessoaFisica funcao-historico=\"historico(ev, pessoa)\"></dados-empresariais></div></md-tab-body></md-tab><md-tab id=dadosContato><md-tab-label>{{\'Dados De Contato\' | uppercase}}</md-tab-label><md-tab-body><div ng-cloak flex=100 flex-xs=100 class=margin-conteudo offset=5><dados-de-contato responsavel=responsavel historico-pessoa-fisica=historicoPessoaFisica funcao-historico=\"historico(ev, pessoa)\"></dados-de-contato></div></md-tab-body></md-tab><md-tab id=socios><md-tab-label>{{\'Socios\' | uppercase}}</md-tab-label><md-tab-body><div ng-cloak flex=100 flex-xs=100 class=margin-conteudo offset=5><socios responsavel=responsavel></socios></div></md-tab-body></md-tab><md-tab id=relacionamentoSpu><md-tab-label>{{\'Relacionamento SPU\' | uppercase}}</md-tab-label><md-tab-body><div ng-cloak flex=100 flex-xs=100 class=margin-conteudo offset=5><relacionamento-spu></relacionamento-spu></div></md-tab-body></md-tab></md-tabs></md-content><div layout=row layout-align=\"end center\"><md-button class=md-raised ng-click=fechar()>{{\'Fechar\' | uppercase}}</md-button></div></div>');
$templateCache.put('scripts-componentes/cabecalho/cabecalho.html','<!-- Barra de navageção --><md-toolbar layout=row class=md-whiteframe-z3 ng-controller=\"MenuController as ul\"><!-- hide-gt-sm --><md-button class=menu ng-click=ul.toggleMenu() aria-label=\"Menu da Aplicação\"><ng-md-icon icon=menu style=fill:#fff size=38></ng-md-icon></md-button><div layout=column hide-xs layout-align=\"center center\"><a ui-sref=HOME><img src=assets/img/logo-spu.png></a></div><div layout=row class=icones-menu flex=75 layout-align=\"end center\"><a href={{urlHome}} class=posicao-itens-barra-menu><ng-md-icon icon=home size=35 alt=Home><md-tooltip md-direction=bottom>Página inicial</md-tooltip></ng-md-icon></a><a href={{urlPrefixo}}/demonstra/html/index.html target=_blank><ng-md-icon icon=help size=35 alt=Ajuda><md-tooltip md-direction=bottom>Ajuda</md-tooltip></ng-md-icon></a><a href={{urlPrefixo}}/api/saml/logout><ng-md-icon icon=do_not_disturb size=35 alt=Notificações><md-tooltip md-direction=bottom>Logout</md-tooltip></ng-md-icon></a></div></md-toolbar><md-toolbar layout=row class=md-whiteframe-z3 style=\"min-height: 32px !important; background: #38813C !important\"><div flex=60 ncy-breadcrumb></div><div flex layout=row layout-align=\"end center\"><contador-sessao></contador-sessao></div><!-- Container Menu e Conteúdo --></md-toolbar><!-- MENU --><menu>');
$templateCache.put('scripts-componentes/menu/menu.html','<!-- Menu --><!-- md-is-locked-open=\"$mdMedia(\'gt-sm\')\" --><md-sidenav class=\"site-sidenav md-fg-600 md-sidenav-left md-whiteframe-z2\" md-component-id=left layout=column flex><div style=\"background: rgb(76,175,80); min-height: 88px; width: 100%\"></div><div layout=column style=\"width: 100%\"><md-menu-bar role=menubar><md-menu md-position-mode style=\"margin: 0px; padding: 0px\" ng-repeat=\"item in getMenuAcessosFiltrado()\"><md-button ng-click=$mdOpenMenu()><div layout=row flex>{{item.descricaoResumida}} <span flex></span> <span><i style=\"vertical-align: middle\" class=material-icons>chevron_right</i></span></div></md-button><md-menu-content><md-menu-item><div layout=column><ul ng-repeat=\"subItemPai in item.nos\"><li><h3 style=\"color: rgb(76,175,80)\">{{subItemPai.descricaoResumida}}</h3><ul class=listaitens><li class=itensmenu ng-repeat=\"subItemFilho in subItemPai.nos\"><md-button href={{getUrl(subItemFilho)}} ng-click=closeMenu() ng-if=\"subItemFilho.codigoModulo && !isMenuSiapa(subItemFilho)  && !isMenuSistemaExterno(subItemFilho)\">{{subItemFilho.descricaoResumida}}</md-button><md-button ng-click=clickMenuSiapa(subItemFilho) ng-if=isMenuSiapa(subItemFilho)>{{subItemFilho.descricaoResumida}}</md-button><md-button ng-click=\"clickMenuSistemaExterno(item, subItemFilho)\" ng-if=isMenuSistemaExterno(subItemFilho)>{{subItemFilho.descricaoResumida}}</md-button></li></ul></li></ul></div></md-menu-item></md-menu-content></md-menu></md-menu-bar></div></md-sidenav>');
$templateCache.put('scripts-componentes/rodape/rodape.html','<footer class=footer>SPU - Secretaria de Patrimônio da União | Esplanada dos Ministérios - Bloco C e K - Brasília - DF</footer>');
$templateCache.put('scripts-componentes/tituloServico/tituloServico.html','<div class=texto-principal>{{(dinamico) ? tituloStatico : buscarTitulo()}}</div>');
$templateCache.put('scripts-componentes/toast/toast-template-error.html','<md-toast md-theme=error-toast><div class=toast-padding layout=column ng-show=isArray><h3 flex>{{msg}}</h3><!-- <div flex ng-repeat=\"msg in msgs\">\n            <span flex>{{msg}}</span>\n        </div> --></div><span flex ng-show=!isArray><h3>{{msgs}}<br></h3></span></md-toast>');
$templateCache.put('scripts-componentes/toast/toast-template-success.html','<md-toast md-theme=success-toast><span flex>{{msgs}}</span></md-toast>');
$templateCache.put('scripts-componentes/toast/toast-template-warning.html','<md-toast md-theme=warning-toast><span flex>{{msgs}}</span></md-toast>');
$templateCache.put('scripts-componentes/infoconv/componentes/dadosDeContato/templates/dadosDeContato.html','<v-accordion class=vAccordion--default style=\"margin-top: 20px\"><v-pane multiple expanded=true><v-pane-header id=documento class=\"titulo-verde negrito\">{{\'Dados de Contato\' | uppercase}}</v-pane-header><v-pane-content><div><div ng-repeat=\"telefone in responsavel.telefone track by $index\" style=\"display: inline-block; width: calc(100% / 3)\"><label style=\"padding-left: 0px !important\">Telefone {{$index + 1}}</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">({{telefone.ddd}}) {{telefone.numero}}<md-tooltip md-direction=right style=\"margin-left: -10%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b></div></div><label>E-mail</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b></v-pane-content><v-pane-header id=enderecamento class=\"titulo-verde negrito\">{{\'Endereçamento\' | uppercase}}</v-pane-header><v-pane-content><div><div layout=row><div layout=column flex=50><b><span>RECEITA FEDERAL <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaFisica.endereco})\"><ng-md-icon ng-if=\"historicoPessoaFisica.endereco.length > 1\" icon=history size=20></ng-md-icon></a></span></b></div><div layout=column flex=50 layout-align=end><span style=\"text-align: right\">Data da Atualização: {{responsavel.dataCriacao | date: \'dd/MM/yyyy\'}}</span></div></div><hr><div layout=row><div layout=column flex=33 flex-xs=100><label>País</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Brasil</p></b><label>Município</label><b><p ng-bind=\"responsavel.endereco.municipio ? responsavel.endereco.municipio : \'Sem informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.endereco.municipio style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Nº</label><b><p ng-bind=\"responsavel.endereco.numero ? responsavel.endereco.numero : \'Sem informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.endereco.numero style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b></div><div layout=column flex=33 flex-xs=100><label>Cep</label><b><p ng-bind=\"responsavel.endereco.cep ? responsavel.endereco.cep : \'Sem informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.endereco.cep style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Tipo de Logradouro</label><b><p ng-bind=\"responsavel.endereco.tipoLogradouro ? responsavel.endereco.tipoLogradouro : \'Sem informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.endereco.tipoLogradouro style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Bairro/Distrito</label><b><p ng-bind=\"responsavel.endereco.bairro ? responsavel.endereco.bairro : \'Sem informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.endereco.bairro style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b></div><div layout=column flex=34 flex-xs=100><label>UF</label><b><p ng-bind=\"responsavel.endereco.uf ? responsavel.endereco.uf  : \'Sem informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.endereco.uf style=\"margin-left: -2%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Logradouro</label><b><p ng-bind=\"responsavel.endereco.logradouro ? responsavel.endereco.logradouro : \'Sem informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.endereco.logradouro style=\"margin-left: -2%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Complemento</label><b><p ng-bind=\"responsavel.endereco.complemento ? responsavel.endereco.complemento : \'Sem informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.endereco.complemento style=\"margin-left: -2%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b></div></div></div><div style=\"margin-top: 20px\"><div layout=row><div layout=column flex=50><b><span>SPUNet(CORRESPONDÊNCIA)</span></b></div><div layout=column flex=50 layout-align=end><span style=\"text-align: right\">Data da Atualização: DD/MM/AAAA</span></div></div><hr><div layout=row><div layout=column flex=33 flex-xs=100><label>País</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Município</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Nº</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b></div><div layout=column flex=33 flex-xs=100><label>Cep</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Tipo de Logradouro</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Bairro/Distrito</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b></div><div layout=column flex=34 flex-xs=100><label>UF</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Logradouro</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Complemento</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b></div></div></div><div style=\"margin-top: 20px\"><div layout=row><div layout=column flex=50><b><span>CADÚnico</span></b></div><div layout=column flex=50 layout-align=end><span style=\"text-align: right\">Data da Atualização: DD/MM/AAAA</span></div></div><hr><div layout=row><div layout=column flex=33 flex-xs=100><label>País</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Município</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Nº</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b></div><div layout=column flex=33 flex-xs=100><label>Cep</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Tipo de Logradouro</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Bairro/Distrito</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b></div><div layout=column flex=34 flex-xs=100><label>UF</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Logradouro</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Complemento</label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b></div></div></div></v-pane-content></v-pane></v-accordion>');
$templateCache.put('scripts-componentes/infoconv/componentes/dadosEmpresariais/modal/modalHistorico.html','<md-dialog flex=40 flex-xs=100><md-toolbar><div style=\"font-size: 20px; font-family:\'Open Sans\', sans-serif;color: #FFFFFF\" class=\"md-toolbar-tools titulo-modal\"><h2>Histórico</h2><span flex></span> <a href=\"\" ng-click=fechar()><ng-md-icon style=fill:#ffffff icon=close></ng-md-icon></a></div></md-toolbar><md-dialog-content><div layout=row style=\"padding:20px 20px 0 20px\"><div flex-xs=100 flex=33 layout=column><b>Data</b></div><div flex-xs=100 flex=33 layout=column><b>Fonte</b></div><div flex-xs=100 flex=34 layout=column><b>Valor</b></div></div><div layout=row style=\"padding: 0 20px 0 20px\" ng-repeat=\"historico in historico\"><div flex=33 layout=column><p ng-bind=\"historico.data | date: \'dd/MM/yyyy\'\"></p></div><div flex=33 layout=column><p ng-bind=historico.fonte></p></div><div flex=33 layout=column><p ng-bind=\"historico.valor.logradouro ? historico.valor.cep + \' / \' + historico.valor.uf + \' / \' + historico.valor.municipio + \' / \'\n                + historico.valor.tipoLogradouro + \' / \' + historico.valor.logradouro + \' / \' + historico.valor.numero + \' / \'\n                + historico.valor.bairro + \' / \' + historico.valor.complemento\n                : historico.valor\"></p></div></div></md-dialog-content></md-dialog>');
$templateCache.put('scripts-componentes/infoconv/componentes/dadosEmpresariais/templates/dadosEmpresariais.html','<div><div layout=row><div style=\"margin-top: 20px\" flex-xs=100 flex-sm=100 flex=33 layout=column><label>CNPJ <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.cnpj.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.cnpj | cpfcnpj\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.cnpj style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Nome <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.nomeEmpresarial.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.nomeEmpresarial || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.nomeEmpresarial style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Nome do Responsavel <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.nomeResponsavel.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.nomeResponsavel || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.nomeResponsavel style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Código e Descrição CNAE Principal <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.cnaePrincipal.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.cnaePrincipal || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.cnaePrincipal style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>CNPJ Sucedida <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.cnpjSucedida.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.cnpjSucedida || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.cnpjSucedida style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Cadastro CEBAS Saúde? <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.cebas.saude.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.cebas.saude ? \'Sim\' : \'Não\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.cebas.saude style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Cadastro CEBAS Assistencial? <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.cebas.assistencial.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.cebas.assistencial ? \'Sim\' : \'Não\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.cebas.assistencial style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b></div><div style=\"margin-top: 20px\" flex-xs=100 flex-sm=100 flex=33 layout=column><label>Situação Cadastral do CNPJ <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.situacaoCadastral.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.situacaoCadastral || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.situacaoCadastral style=\"margin-left: -25%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Nome Fantasia <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.nomeFantasia.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.nomeFantasia || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.nomeFantasia style=\"margin-left: -25%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>CPF do Responsavel <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.cpfResponsavel.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.cpfResponsavel || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.cpfResponsavel style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Código e Descrição CNAE Secundário <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.cnaeSecundario.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.cnaeSecundario || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.cnaeSecundario style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>CNPJ Sucessora <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.cnpjSucedida.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.cnpjsSucessora[0] || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.cnpjSucedida style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Data de Inicio da Vigencia <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.dataInicioVigencia.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.dataInicioVigencia || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.dataInicioVigencia>Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Status da Certificacao <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.statusCertificacao.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.statusCertificacao || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.statusCertificacao>Fonte: {{responsavel.fonte}}</md-tooltip></p></b></div><div style=\"margin-top: 20px\" flex-xs=100 flex-sm=100 flex=34 layout=column><label>Data da Situação Cadastral <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaJuridica.dataSituacaoCadastral})\"><ng-md-icon ng-if=\"historicoPessoaJuridica.dataSituacaoCadastral.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.dataSituacaoCadastral | date: \'dd/MM/yyyy\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.dataSituacaoCadastral>Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Natureza Jurídica <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.naturezaJuridica.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.naturezaJuridica || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.naturezaJuridica>Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Data de Abertura <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.dataAbertura.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.dataAbertura | date: \'dd/MM/yyyy\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.dataAbertura>Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>UG <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.ug.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.ug || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.ug>Fonte: {{responsavel.fonte}}</md-tooltip></p></b><div style=\"margin: 1.9em 0\"></div><label>Data de Fim da Vigencia <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=\"historicoPessoaJuridica.dataFimVigencia.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.dataFimVigencia || \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.dataFimVigencia>Fonte: {{responsavel.fonte}}</md-tooltip></p></b></div></div><v-accordion class=vAccordion--default><v-pane expanded=true><v-pane-header id=documento class=\"titulo-verde negrito\">{{\'Documentos\' | uppercase}}</v-pane-header><v-pane-content><md-table-container><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row class=upload-head><th md-column>Documento</th><th md-column>Data de Emissão</th><th md-column>Data de Validade</th><th md-column>Origem</th><th md-column>Auteticado</th><th md-column>Arquivo</th></tr></thead><tbody md-body><tr md-row><td md-cell><span>Documento de Identificação</span></td><td md-cell><span>dd/mm/aaaa</span></td><td md-cell><span>dd/mm/aaaa</span></td><td md-cell><span>Requerimento Eletrônico</span></td><td md-cell><md-checkbox class=md-primary ng-model=documento.autenticado disabled></md-checkbox></td><td md-cell><ng-md-icon icon=attach_file ng-click=\"\"></ng-md-icon></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabelaDocumentos.limit md-limit-options=tabelaDocumentos.limitsPage md-page=tabelaDocumentos.page md-total=\"\" md-on-paginate=true style=\"border: 1px solid #cccccc; margin:0 5px 0 5px\"></md-table-pagination></v-pane-content></v-pane></v-accordion></div>');
$templateCache.put('scripts-componentes/infoconv/componentes/dadosPessoais/modal/modalHistorico.html','<md-dialog flex=40 flex-xs=100><md-toolbar><div style=\"font-size: 20px; font-family:\'Open Sans\', sans-serif;color: #FFFFFF\" class=\"md-toolbar-tools titulo-modal\"><h2>Histórico</h2><span flex></span> <a href=\"\" ng-click=fechar()><ng-md-icon style=fill:#ffffff icon=close></ng-md-icon></a></div></md-toolbar><md-dialog-content><div layout=row style=\"padding:20px 20px 0 20px\"><div flex-xs=100 flex=33 layout=column><b>Data</b></div><div flex-xs=100 flex=33 layout=column><b>Fonte</b></div><div flex-xs=100 flex=34 layout=column><b>Valor</b></div></div><div layout=row style=\"padding: 0 20px 0 20px\" ng-repeat=\"historico in historico\"><div flex=33 layout=column><p ng-bind=\"historico.data | date: \'dd/MM/yyyy\'\"></p></div><div flex=33 layout=column><p ng-bind=historico.fonte></p></div><div flex=33 layout=column><p ng-bind=\"historico.valor.logradouro ? historico.valor.cep + \' / \' + historico.valor.uf + \' / \' + historico.valor.municipio + \' / \'\n                + historico.valor.tipoLogradouro + \' / \' + historico.valor.logradouro + \' / \' + historico.valor.numero + \' / \'\n                + historico.valor.bairro + \' / \' + historico.valor.complemento\n                : historico.valor\"></p></div></div></md-dialog-content></md-dialog>');
$templateCache.put('scripts-componentes/infoconv/componentes/dadosPessoais/templates/dadosPessoais.html','<div><div layout=row><div style=\"margin-top: 20px\" flex-xs=100 flex=33 layout=column><label>CPF <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaFisica.cpf})\"><ng-md-icon ng-if=\"historicoPessoaFisica.cpf.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.cpf | cpfcnpj\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.cpf style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Data de Nascimento <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaFisica.dataNascimento})\"><ng-md-icon ng-if=\"historicoPessoaFisica.dataNascimento.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.dataNascimento | date: \'dd/MM/yyyy\';\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.dataNascimento style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Sexo <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaFisica.sexo})\"><ng-md-icon ng-if=\"historicoPessoaFisica.sexo.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.sexo ? responsavel.sexo : \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.sexo style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Estrangeiro? <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaFisica.estrangeiro})\"><ng-md-icon ng-if=\"historicoPessoaFisica.estrangeiro.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.estrangeiro ? \'Sim\' : \'Não\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Titulo de Eleitor <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaFisica.tituloEleitor})\"><ng-md-icon ng-if=\"historicoPessoaFisica.tituloEleitor.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.tituloEleitor ? responsavel.tituloEleitor : \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.tituloEleitor style=\"margin-left: -22%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Portador de Necessidades Especiais? <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=false icon=history size=20></ng-md-icon></a></label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b></div><div style=\"margin-top: 20px\" flex-xs=100 flex=33 layout=column><label>Situação Cadastral do CPF <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaFisica.situacaoCadastral})\"><ng-md-icon ng-if=\"historicoPessoaFisica.situacaoCadastral.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.situacaoCadastral ? responsavel.situacaoCadastral : \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.situacaoCadastral style=\"margin-left: -25%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Ano Óbito <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaFisica.anoObito})\"><ng-md-icon ng-if=\"historicoPessoaFisica.anoObito.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.anoObito ? responsavel.anoObito : \'Sem informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.anoObito style=\"margin-left: -25%\">Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Estado Civil <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=false icon=history size=20></ng-md-icon></a></label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Indigena? <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=false icon=history size=20></ng-md-icon></a></label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Número NIS <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=false icon=history size=20></ng-md-icon></a></label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b></div><div style=\"margin-top: 20px\" flex-xs=100 flex=34 layout=column><label>Nome <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaFisica.nome})\"><ng-md-icon ng-if=\"historicoPessoaFisica.nome.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.nome ? responsavel.nome : \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.nome>Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Nome da Mãe <a href=\"\" ng-click=\"funcaoHistorico({ev: $event, pessoa: historicoPessoaFisica.nomeMae})\"><ng-md-icon ng-if=\"historicoPessoaFisica.nomeMae.length > 1\" icon=history size=20></ng-md-icon></a></label><b><p ng-bind=\"responsavel.nomeMae ? responsavel.nomeMae : \'Sem Informação\'\" style=\"margin-top: 0px; padding-left: 0px !important\"><md-tooltip md-direction=right ng-if=responsavel.nomeMae>Fonte: {{responsavel.fonte}}</md-tooltip></p></b><label>Nome do Conjuge/Companheiro(a) <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=false icon=history size=20></ng-md-icon></a></label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem Informação</p></b><label>Quilombola? <a href=\"\" ng-click=\"\"><ng-md-icon ng-if=false icon=history size=20></ng-md-icon></a></label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b><label>Pertence a Comunidade Tradicional?<a href=\"\" ng-click=\"\"><ng-md-icon ng-if=false icon=history size=20></ng-md-icon></a></label><b><p style=\"margin-top: 0px; padding-left: 0px !important\">Sem informação</p></b></div></div><v-accordion class=vAccordion--default><v-pane expanded=true><v-pane-header id=vinculoPessoaJuridica class=\"titulo-verde negrito\">{{\'Vinculação à Pessoa Juridica\' | uppercase}}</v-pane-header><v-pane-content><md-table-container><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row class=upload-head><th md-column>Vinculo</th><th md-column>CNPJ</th><th md-column>Nome da Empresa</th></tr></thead><tbody md-body><tr md-row><td md-cell colspan=3 style=\"text-align: center!important\"><span>Sem Informação</span></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabelaVinculacaoPessoaJuridica.limit md-limit-options=tabelaVinculacaoPessoaJuridica.limitsPage md-page=tabelaVinculacaoPessoaJuridica.page md-total=\"\" md-on-paginate=true style=\"border: 1px solid #cccccc; margin:0 5px 0 5px\"></md-table-pagination></v-pane-content></v-pane></v-accordion><v-accordion class=vAccordion--default><v-pane expanded=true><v-pane-header id=documento class=\"titulo-verde negrito\">{{\'Documentos\' | uppercase}}</v-pane-header><v-pane-content><md-table-container><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row class=upload-head><th md-column>Documento</th><th md-column>Data de Emissão</th><th md-column>Data de Validade</th><th md-column>Origem</th><th md-column>Autenticado</th><th md-column>Arquivo</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in documentoPessoaFisica\"><td md-cell><span>{{documento.nome}}</span></td><td md-cell><span style=\"color: red\">Não Possui</span></td><td md-cell><span style=\"color: red\">Não Possui</span></td><td md-cell><span style=\"color: red\">Não Possui</span></td><td md-cell><!--<md-checkbox class=\"md-primary\" ng-model=\"documento.autenticado\" disabled></md-checkbox>--> <span style=\"color: red\">Não Possui</span></td><td md-cell><span style=\"color: red\">Não Possui</span><!--<ng-md-icon icon=\"attach_file\"\n                                            ng-click=\"\">\n                                </ng-md-icon>--></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabelaDocumentos.limit md-limit-options=tabelaDocumentos.limitsPage md-page=tabelaDocumentos.page md-total={{documentoPessoaFisica.length}} md-on-paginate=true style=\"border: 1px solid #cccccc; margin:0 5px 0 5px\"></md-table-pagination></v-pane-content></v-pane></v-accordion></div>');
$templateCache.put('scripts-componentes/infoconv/componentes/dadosProfissionais/templates/dadosProfissionais.html','<v-accordion class=vAccordion--default style=\"margin-top: 20px\"><v-pane multiple expanded=true><v-pane-header id=dadosProfissionais class=\"titulo-verde negrito\">{{\'Dados Profissionais\' | uppercase}}</v-pane-header><v-pane-content><div><div layout=row><div layout=column flex=50><b><span>[Ocupação 1]</span></b></div><div layout=column flex=50 layout-align=end><span style=\"text-align: right\">Data da Atualização: DD/MM/AAAA</span></div></div><hr><div layout=row><div layout=column flex=33 flex-xs=100><label>Natureza da Ocupação</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Servidor Federal</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Órgão/Entidade de Exercício</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Data de Admissão</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b></div><div layout=column flex=33 flex-xs=100><label>Ocupação Principal</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Matrícula SAIPE</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Função</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Data de Desligamento</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b></div><div layout=column flex=34 flex-xs=100><label>Número CTPS</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Local de Exercício</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Data da última alteração na função</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b></div></div></div><div style=\"margin-top: 20px\"><div layout=row><div layout=column flex=50><b><span>[OCUPAÇÃO X]</span></b></div><div layout=column flex=50 layout-align=end><span style=\"text-align: right\">Data da Atualização: DD/MM/AAAA</span></div></div><hr><div layout=row><div layout=column flex=33 flex-xs=100><label>Natureza da Ocupação</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Empregador</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b></div><div layout=column flex=33 flex-xs=100><label>Ocupação Principal</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b></div><div layout=column flex=34 flex-xs=100><label>Número CTPS</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b></div></div></div></v-pane-content><v-pane-header id=dadosRendaPatrimonio class=\"titulo-verde negrito\">{{\'Dados de Renda/Patrimônio\' | uppercase}}</v-pane-header><v-pane-content><div layout=row><div layout=column flex=33 flex-xs=100><label>Renda(R$)</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Possui Veiculo?</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b></div><div layout=column flex=33 flex-xs=100><label>Renda Familiar(R$)</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Integra o CADÚnico?</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b></div><div layout=column flex=33 flex-xs=100><label>Declara ser baixa renda?</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b><label>Possui Imóvel?</label><b><p style=\"margin-top: 0px\">Sem Informação</p></b></div></div></v-pane-content></v-pane></v-accordion>');
$templateCache.put('scripts-componentes/infoconv/componentes/relacionamentoSpu/templates/relacionamentoSpu.html','<v-accordion class=vAccordion--default style=\"margin-top: 20px\"><v-pane expanded=true><v-pane-header id=destinacao class=\"titulo-verde negrito\">{{\'Destinação\' | uppercase}}</v-pane-header><v-pane-content><md-table-container><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row class=upload-head><th md-column>Código da Utilização</th><th md-column>Instrumento</th><th md-column>Início da Vigência</th><th md-column>Fim da Vigência</th></tr></thead><tbody md-body><tr md-row ng-if=\"destinacaoPorResponsavel.length > 0\" ng-repeat=\"destinacao in destinacaoPorResponsavel\"><td md-cell style=\"padding-left: 18px !important\">{{destinacao.rip + \'/\' + destinacao.codigoUtilizacao + \'/\' + lpad(destinacao.parcela)}}</td><td md-cell>{{destinacao.instrumento}}</td><td md-cell>{{destinacao.inicioVigencia | date: \'dd/MM/yyyy\'}}</td><td md-cell>{{destinacao.fimVigencia | date: \'dd/MM/yyyy\'}}</td></tr><tr md-row ng-if=\"!destinacaoPorResponsavel.length > 0\"><td md-cell colspan=4 style=\"text-align: center!important\"><span>Não existe destinações para esta pessoa</span></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabelaDestinacao.limit md-limit-options=tabelaDestinacao.limitsPage md-page=tabelaDestinacao.page md-total=destinacaoPorResponsavel.length md-on-paginate=true style=\"border: 1px solid #cccccc; margin:0 5px 0 5px\"></md-table-pagination></v-pane-content></v-pane></v-accordion>');
$templateCache.put('scripts-componentes/infoconv/componentes/socios/templates/socios.html','<v-accordion class=vAccordion--default><v-pane expanded=true><v-pane-header id=socios class=\"titulo-verde negrito\">{{\'Socios\' | uppercase}}</v-pane-header><v-pane-content><md-table-container><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row class=upload-head><th md-column>CPF</th><th md-column>Nome</th><th md-column>Tipo</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"socio in sociosPaginados\"><td md-cell><a href ng-click=detalharPessoa(socio.numero)>{{socio.numero}}</a></td><td md-cell><span>{{socio.nome}}</span></td><td md-cell><span>{{socio.tipo}}</span></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabelaSocios.limit md-limit-options=tabelaSocios.limitsPage md-page=tabelaSocios.page md-total={{responsavel.socios.length}} md-on-paginate=paginacaoSocios style=\"border: 1px solid #cccccc; margin:0 5px 0 5px\" md-page-select></md-table-pagination></v-pane-content></v-pane></v-accordion>');}]);})();
(function () {
  'use strict';

  angular.module('su-integrador', [
    'su-componentes'
  ]);

})();

  (function(){
    'use strict';
    angular.module('su-integrador')
      .controller('suspenderInstrumentoController',
        ['$scope', 'escopoCompartilhadoService','$state', '$filter', '$mdDialog','ComumFactory','instrumentoFuncionalidadeService',
          function ($scope,escopoCompartilhadoService,$state, $filter, $mdDialog, ComumFactory, instrumentoFuncionalidadeService) {

            var vm = $scope;

            vm.suspender = {};
            vm.responsavel = {};
            vm.suspender.dataSuspensao = new Date();


            vm.cancelar = cancelar;
            vm.confirmar = confirmar;


            function init() {
              vm.suspender.instrumentoFuncionalidadeCadastrado = escopoCompartilhadoService.getObjeto('funcionalidade');
              escopoCompartilhadoService.limparEscopo();
              if(!vm.suspender.instrumentoFuncionalidadeCadastrado) {
                window.history.back();
              }
              instrumentoFuncionalidadeService.getUsuario().then(function (resultado) {
                vm.responsavel.nome = resultado.data.resposta[0].nome;
                vm.responsavel.cpf = resultado.data.resposta[0].cpf;
                vm.suspender.responsavel = vm.responsavel.nome + ' / ' + vm.responsavel.cpf;
              })

            }

            function confirmar(ev) {
              if(vm.formSuspender.$invalid){
                var mensagem = $filter('translate')('msg-justificativa');
                ComumFactory.mostrarMensagemError(mensagem);
                return ;
              }
              var confirm = $mdDialog.confirm()
                .title('Suspender Instrumento')
                .textContent('Tem certeza que deseja suspender este instrumento/funcionalidade?')
                .targetEvent(ev)
                .ok('Confirmar')
                .cancel('Cancelar');
              $mdDialog.show(confirm).then(function() {
                suspenderInstrumento();

              }, function() {
              });
            }

            function cancelar(){
              escopoCompartilhadoService.limparEscopo();
              $state.go('INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE');
            }

            function suspenderInstrumento() {
              var mensagem;
              if(vm.suspender.instrumentoFuncionalidadeCadastrado.suspenso === true){
                mensagem = $filter('translate')('msg-instrumento-funcionalidade-suspendida');
              }else{
                mensagem = $filter('translate')('msg-instrumento-funcionalidade-n-suspensa');
              }
              instrumentoFuncionalidadeService.suspenderInstrumento(vm.suspender).then(function () {
                ComumFactory.mostrarMensagemSucesso(mensagem);
                $state.go('INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE');
              });

            }

            init();

          }]);
  })();

/**
 * Created by haillanderson on 15/02/17.
 */

(function () {
  'use strict';
  controller.$inject = ["$mdDialog"];
  angular
    .module('su-integrador')
    .controller('removerFundamentoLegalController', controller);

  function controller($mdDialog) {

    var vm = this;

    vm.fechar = fechar;
    vm.cancelar = cancelar;
    vm.confirmar = confirmar;
    vm.result;

    function fechar() {
      cancelar();
    }

    function cancelar() {
      vm.result = false;
      $mdDialog.hide(vm.result);
    }

    function confirmar() {
      vm.result= true;
      $mdDialog.hide(vm.result);
    }


  }
})();

(function () {
  'use strict';
  controller.$inject = ["instrumentoFuncionalidadeService", "$mdDialog", "funcionalidade", "$filter", "comumService"];
  angular
    .module('su-integrador')
    .controller('justificativaController', controller);

  function controller(instrumentoFuncionalidadeService, $mdDialog, funcionalidade, $filter, comumService ) {

    var vm = this;

    vm.fechar = fechar;
    vm.cancelar = cancelar;
    vm.confirmar = confirmar;
    vm.suspenderInstrumento = suspenderInstrumento;

    vm.funcionalidade = funcionalidade;

    function suspenderInstrumento(funcionalidade, ev) {
      if(vm.formSuspender.$invalid){
        var mensagem = $filter('translate')('msg-justificativa');
        comumService.mostrarMensagemError(mensagem);
        return ;
      }
      var confirm = $mdDialog.confirm()
        .title('Suspender Instrumento')
        .textContent('Tem certeza que deseja suspender este instrumento/funcionalidade?')
        .targetEvent(ev)
        .ok('Confirmação')
        .cancel('Cancelamento');
      $mdDialog.show(confirm).then(function() {
        confirmar(vm.funcionalidade);
      }, function() {
        cancelar();
      });
    }

    function fechar() {
      $mdDialog.cancel();
    }

    function cancelar() {
      vm.funcionalidade.funcionalidadeSuspenca = false;
      fechar()
    }

    function confirmar() {
      instrumentoFuncionalidadeService.salvar(vm.funcionalidade);
      fechar();
    }


  }
})();

/**
 * Created by Basis Tecnologia on 21/12/2016.
 */
(function () {
  'use strict';
  controller.$inject = ["instrumentoFuncionalidadeService", "ComumFactory", "escopoCompartilhadoService", "$mdDialog", "instrumento", "listaFundamentosAlterados", "idEdicao", "$filter"];
  angular
    .module('su-integrador')
    .controller('fundamentoLegalController', controller);

  function controller(instrumentoFuncionalidadeService, ComumFactory, escopoCompartilhadoService,
                      $mdDialog, instrumento, listaFundamentosAlterados, idEdicao, $filter) {

    var vm = this;

    var setarOrdem =  function(lista) {
      angular.forEach(lista, function (item, $index) {
        item.ordem = $index;
      });
      return lista;
    };

    var init = function () {
      vm.ordemCampos = instrumentoFuncionalidadeService.ordenaLista;
      inicializarFundamento();
    };
    vm.listaFundamentosAlterados = listaFundamentosAlterados;
    vm.instrumento = instrumento;
    vm.idEdicao = idEdicao;
    vm.indexTemporario = '';
    vm.editar = false;


    vm.camposEdicaoTextAngular =
      [
        ['h1', 'h2', 'h3', 'p'],
        ['bold', 'italics', 'underline', 'ul', 'ol'],
        ['justifyLeft', 'justifyCenter', 'justifyRight', 'indent', 'outdent'],
        ['html','insertLink']
      ];

    vm.tabelaFundamentos = {
      order:'ordem',
      limit: 5,
      limitsPage: [5, 10, 20],
      page: 1
    };

    vm.adicionarFundamentoLegal = adicionarFundamentoLegal;
    vm.confirmarEdicao = confirmarEdicao;
    vm.cancelarEdicao = cancelarEdicao;
    vm.editarObjeto = editarObjeto;
    vm.removerItem = removerItem;
    vm.confirmar = confirmar;
    vm.salvarFundamentoLegalTemp = salvarFundamentoLegalTemp;
    vm.fechar = fechar;
    vm.ativarModoConsulta = ativarModoConsulta;
    vm.ativarModoInclusao = ativarModoInclusao;
    vm.consultarFundamentoLegal = consultarFundamentoLegal;
    vm.inicializarFundamento = inicializarFundamento;
    vm.formatoEdicao = formatoEdicao;

    function formatoEdicao() {
      return vm.idEdicao;
    }

    function inicializarFundamento() {
      vm.fundamentoLegal = {id:'', descricao:'', ativo: true, registroVinculado: false, ordem: 0};
    }

    function adicionarFundamentoLegal(){
      if(vm.fundamentoLegal.descricao == ''){
        ComumFactory.mostrarMensagemError('Preencher todos os campos');
        return;
      }
      if(vm.instrumento.listFundamentoLegais == null || vm.instrumento.listFundamentoLegais == undefined){
          vm.instrumento.listFundamentoLegais = [];
      }
      vm.instrumento.listFundamentoLegais.push(vm.fundamentoLegal);
      inicializarFundamento();
    }

    function cancelarEdicao() {
      vm.editar = false;
      inicializarFundamento();
    }

    function editarObjeto(lista, fundamento) {
      vm.editar = true;
      vm.fundamentoLegal = angular.copy(lista[lista.indexOf(fundamento)]);
      vm.fundamentoLegal.descricao = ComumFactory.removerHTML(vm.fundamentoLegal.descricao);
      vm.indexTemporario = lista.indexOf(fundamento);
    }

    function confirmarEdicao() {
      if(vm.modoConsulta){
        vm.fundamamentoLegalConsulta[vm.indexTemporario] = vm.fundamentoLegal;
        var id =  vm.fundamamentoLegalConsulta[vm.indexTemporario].id;
        var descricao =  vm.fundamamentoLegalConsulta[vm.indexTemporario].descricao;
        enviarParaListaInstrumento(id, descricao);
      }else{
        vm.instrumento.listFundamentoLegais[vm.indexTemporario] = vm.fundamentoLegal;
      }
      vm.listaFundamentosAlterados.itensAlterados.push(vm.fundamentoLegal);
      vm.editar = false;
      inicializarFundamento();
    }

    function enviarParaListaInstrumento(id, descricao){
      angular.forEach( vm.instrumento.listFundamentoLegais, function(item){
            if(item.id == id){
              item.descricao = descricao;
            }
        });
    }

    function removerItem(ev, fundamento, lista) {
      if(verificarFundamentosVinculadosEmChecklist(fundamento)){
        $mdDialog.show({
          preserveScope: true,
          autoWrap: true,
          skipHide: true,
          controller: 'removerFundamentoLegalController',
          controllerAs: 'removerFundamentoCtrl',
          templateUrl: 'scripts-integrador/pages/instrumento-funcionalidade/view/partials/removerFundamento.html',
          parent: angular.element(document.body),
          targetEvent: ev,
          clickOutsideToClose: false
        }).then(function(result){
            if(result){
              removerFundamentoChecklist(fundamento, lista);
            }
        })
      }else{
        removerFundamentoChecklist(fundamento, lista);
      }
    }

    function removerFundamentoChecklist(fundamento, lista){
      vm.listaFundamentosAlterados.itensExcluidos.push(fundamento);
      lista.splice(lista.indexOf(fundamento), 1);
      vm.instrumento.listFundamentoLegais = lista;
      vm.fundamamentoLegalConsulta = lista;
    }

    function verificarFundamentosVinculadosEmChecklist(fundamento) {
      vm.verificador = false;
      angular.forEach(vm.instrumento.listChecklist, function (listaCheckLists) {
        angular.forEach(listaCheckLists.fundamentoLegalList, function (fundamentoLegal) {
          if(fundamento.id == fundamentoLegal.id){
             vm.verificador = true;
          }
        });
      });
      return vm.verificador;
    }

    function confirmar() {
      setarOrdem(vm.instrumento.listFundamentoLegais);
      vm.salvarFundamentoLegalTemp();
    }

    function salvarFundamentoLegalTemp(){
      vm.result = {
        instrumento: vm.instrumento,
        listaFundamentosAlterados: vm.listaFundamentosAlterados
      };

      instrumentoFuncionalidadeService.salvarFundamentoLegalTemp(vm.instrumento).then(function(retorno){
        vm.instrumento.listFundamentoLegais = retorno.data.resposta;
        $mdDialog.hide(vm.result);
        escopoCompartilhadoService.setObjetos('listFundamentoLegal', vm.instrumento.listFundamentoLegais);
      });
    }

    function fechar() {
      $mdDialog.cancel();
    }

    function ativarModoConsulta() {
      vm.modoConsulta = true;
      vm.editar = false;
      vm.fundamamentoLegalConsulta = [];
      inicializarFundamento();
    }

    function ativarModoInclusao() {
      vm.modoConsulta = false;
      vm.editar = false;
      inicializarFundamento();
    }
    function consultarFundamentoLegal() {
      instrumentoFuncionalidadeService.buscarFundamentoLegalPorDescricao(vm.instrumento.id, vm.fundamentoLegal.descricao).then(function(retorno){
        vm.fundamamentoLegalConsulta = retorno.data.resposta;
        if(vm.fundamamentoLegalConsulta.length == 0){
          ComumFactory.mostrarMensagemError($filter('translate')('msg-nenhum-registro-encontrado'));
          return;
        }
      })
    }

    init();
  }
})();

/**
 * Created by Basis Tecnologia on 21/12/2016.
 */
(function () {
  'use strict';
  controller.$inject = ["instrumentoFuncionalidadeService", "ComumFactory", "escopoCompartilhadoService", "$mdDialog", "instrumento", "idEdicao", "$filter"];
  angular
    .module('su-integrador')
    .controller('checkListController', controller);

  function controller(instrumentoFuncionalidadeService, ComumFactory, escopoCompartilhadoService,
                      $mdDialog, instrumento, idEdicao, $filter) {

    var vm = this;

    var setarOrdem =  function(lista) {
      angular.forEach(lista, function (item, $index) {
        item.ordem = $index;
      });
      return lista;
    };

    var init = function () {
      vm.fundamentosLegaisCadastrados = escopoCompartilhadoService.getObjeto('listFundamentoLegal');
      vm.ordemCampos = instrumentoFuncionalidadeService.ordenaLista;
      console.log(vm.fundamentosLegaisCadastrados);
      inicializarCheckList();
    };
    vm.idEdicao = idEdicao;
    vm.instrumento = instrumento;
    vm.indexTemporario = '';
    vm.editar = false;

    vm.camposEdicaoTextAngular =
      [
        ['h1', 'h2', 'h3', 'p'],
        ['bold', 'italics', 'underline', 'ul', 'ol'],
        ['justifyLeft', 'justifyCenter', 'justifyRight', 'indent', 'outdent'],
        ['html','insertLink']
      ];

    vm.tabelaCheckList = {
      order:'ordem',
      limit: 5,
      limitsPage: [5, 10, 20],
      page: 1
    };

    vm.adicionarCheckList = adicionarCheckList;
    vm.confirmarEdicao = confirmarEdicao;
    vm.cancelarEdicao = cancelarEdicao;
    vm.editarObjeto = editarObjeto;
    vm.removerItem = removerItem;
    vm.confirmar = confirmar;
    vm.fechar = fechar;
    vm.formatarString = formatarString;
    vm.ativarModoConsulta = ativarModoConsulta;
    vm.ativarModoInclusao = ativarModoInclusao;
    vm.inicializarCheckList = inicializarCheckList;
    vm.formatoEdicao = formatoEdicao;
    vm.consultarCheckList = consultarCheckList;

    function formatoEdicao() {
      return vm.idEdicao;
    }

    function inicializarCheckList() {
      vm.checkList = {
        id:'',
        descricao: "",
        fundamentoLegalList: [],
        fundamentoLegalListId:[],
        ativo: true,
        registroVinculado: false,
        ordem: 0
      };

    }

    function formatarString(item) {
      return ComumFactory.removerHTML(item);
    }

    function adicionarCheckList() {
      if(vm.checkList.descricao == ''){
        ComumFactory.mostrarMensagemError('Preencher todos os campos');
        return;
      }
      if(vm.instrumento.listChecklist == null || vm.instrumento.listChecklist == undefined){
          vm.instrumento.listChecklist = [];
      }
      vm.instrumento.listChecklist.push(vm.checkList);
      inicializarCheckList();
    }
    function confirmarEdicao() {
      if(vm.modoConsulta){
        vm.checkListConsulta[vm.indexTemporario] = vm.checkList;
        var id =  vm.checkListConsulta[vm.indexTemporario].id;
        var descricao =  vm.checkListConsulta[vm.indexTemporario].descricao;
        enviarParaListaInstrumento(id, descricao);
      }else{
        vm.instrumento.listChecklist[vm.indexTemporario] = vm.checkList;
      }
      vm.editar = false;
      inicializarCheckList();
    }

    function enviarParaListaInstrumento(id, descricao) {
      angular.forEach( vm.instrumento.listChecklist, function(item){
        if(item.id == id){
          item.descricao = descricao;
        }
      });
    }

    function cancelarEdicao() {
      vm.editar = false;
    }

    function editarObjeto(lista, checklist) {
      vm.editar = true;
      vm.checkList = angular.copy(lista[lista.indexOf(checklist)]);
      vm.checkList.descricao = ComumFactory.removerHTML(vm.checkList.descricao);
      vm.indexTemporario = lista.indexOf(checklist);
    }

    function removerItem(ev, checklist, lista) {
      lista.splice(lista.indexOf(checklist), 1);
      vm.checkListConsulta = lista;
      vm.instrumento.listChecklist = lista;
    }

    function confirmar() {
      setarOrdem(vm.instrumento.listChecklist);
      $mdDialog.hide(vm.instrumento);
    }

    function fechar() {
      $mdDialog.cancel();
    }

    function ativarModoConsulta() {
      vm.modoConsulta = true;
      vm.editar = false;
      vm.checkListConsulta = [];
      inicializarCheckList();
    }


    function ativarModoInclusao() {
      vm.modoConsulta = false;
      vm.editar = false;
      inicializarCheckList();
    }

    function consultarCheckList() {
      //Tratamento para quando a lista de fundamentos for nula
      if(vm.checkList.fundamentoLegalList.length == 0 ){
        angular.forEach(vm.instrumento.listFundamentoLegais, function (item){
          vm.checkList.fundamentoLegalListId.push(item.id);
        });
      } else {
        angular.forEach(vm.checkList.fundamentoLegalList, function (item) {
          vm.checkList.fundamentoLegalListId.push(item.id);
        });
      }
      instrumentoFuncionalidadeService.buscarCheckListPorDescricao(vm.instrumento.id, vm.checkList).then(function(retorno){
        vm.checkListConsulta = retorno.data.resposta;
        if(vm.checkListConsulta.length == 0){
          ComumFactory.mostrarMensagemError($filter('translate')('msg-nenhum-registro-encontrado'));
          return;
        }
      })
      vm.checkList.fundamentoLegalListId = [];
    }


    init();
  }
})();

(function(){
  'use strict';

  angular.module('su-integrador')
    .controller('cancelarFundamentoLegalController',
      ["$scope", "$state", "fundamentoLegalService", "integradorEscopoCompartilhadoService", "$filter", "instrumentoFuncionalidadeService", "$mdDialog", "ComumFactory", function ($scope, $state, fundamentoLegalService, integradorEscopoCompartilhadoService,$filter,instrumentoFuncionalidadeService, $mdDialog, ComumFactory) {

        var vm = $scope;

        vm.fechar = fechar;
        vm.confirmar = confirmar;
        var init = function() {
          vm.cancelamento = {};
          vm.responsavel = {};
          vm.cancelamento.dataSuspensao = new Date();

          vm.fundamentoLegal = integradorEscopoCompartilhadoService.getObjeto('fundamentoLegal');
          if(!vm.fundamentoLegal) {
            window.history.back();
          }
          fundamentoLegalService.findById(vm.fundamentoLegal.idFundamento).then(function (resultado) {
              vm.cancelamento.fundamentoLegal = angular.copy(resultado.data.resposta[0]);
              integradorEscopoCompartilhadoService.limparEscopo();
              if(!vm.cancelamento.fundamentoLegal) {
                window.history.back();
              }

          });


          instrumentoFuncionalidadeService.getUsuario().then(function (resultado) {
            vm.responsavel.nome = resultado.data.resposta[0].nome;
            vm.responsavel.cpf = resultado.data.resposta[0].cpf;
            vm.cancelamento.responsavel = vm.responsavel.nome + ' / ' + vm.responsavel.cpf;
          })
        };


        function confirmar(ev) {
          if(vm.formCancelar.$invalid){
            var mensagem = $filter('translate')('Necessário preencher os campos obrigatórios');
            ComumFactory.mostrarMensagemError(mensagem);
            return ;
          }
          var confirm = $mdDialog.confirm()
            .title('Suspender Instrumento')
            .textContent('Tem certeza que deseja cancelar esse fundamento legal?')
            .targetEvent(ev)
            .ok('Confirmar')
            .cancel('Cancelar');
          $mdDialog.show(confirm).then(function() {
              cancelarFundamento()
          }, function() {

          });
        }

        function cancelarFundamento() {
          var mensagem;

          mensagem = $filter('translate')('msg-dado-cancelado-com-sucesso');

          fundamentoLegalService.cancelar(vm.cancelamento).then(function () {
            ComumFactory.mostrarMensagemSucesso(mensagem);
            $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
          });

        }

        function fechar(){
          $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
        }

        init();
      }]);
})();

(function () {

  directive.$inject = ["$rootScope"];
  angular
    .module('su-integrador')
    .directive('titulo', directive);

  function directive($rootScope) {
    return {
      restrict: 'E',
      templateUrl: 'scripts-integrador/app/directives/utilitarios/titulo/template/titulo.html',
      replace: true,
      scope: {
          prefixoTitulo: '='
      },
      link: function (scope) {
        $rootScope.$watch('paginaAtual', function (newValue) {
            if (newValue) {
                if (scope.prefixoTitulo) {
                    scope.titulo = scope.prefixoTitulo + ' ' + newValue;
                } else {
                    scope.titulo = newValue;
                }
            }
        });

      }
    };
  }


})();

/**
 * Created by Basis Tecnologia on 28/07/2016.
 */
angular.module('su-integrador')
    .directive("verificarPermissaoPendencia", ["$rootScope", function ($rootScope) {
            return ({
                link: function link($scope, element, attributes) {
                    var roles = (attributes.verificarPermissaoPendencia || "").split(",");

                    $rootScope.$watch('usuarioLogado', function(value){
                        if(value && $rootScope.usuarioLogado.permissoes.length > 0){
                            for(var i = 0; i < roles.length; i++) {
                                if($rootScope.usuarioLogado.permissoes && $rootScope.usuarioLogado.permissoes.indexOf(roles[i].trim()) != -1) {
                                    break;
                                }else{
                                    //remover evento de click
                                    element.bind('click', function (e) {
                                        e.stopImmediatePropagation();
                                        return false;
                                    });
                                    break;
                                }
                            }
                        }
                    }, true);
                },
                restrict: "A"
            });
        }]
    );

(function(){
    'use strict';

        angular
        .module('su-integrador')
        .service('textosAjudaService', ['$http', service]);

        function service($http){

            var URL_BASE = 'integrador/api';

            var buscarModulos = function(){
                return $http.get(URL_BASE + '/modulos');
            };

            var buscarFuncionalidadePorModuloSemAjuda = function(id){
                return $http.get(URL_BASE + '/funcionalidade/pesquisar-por-modulo-sem-ajuda/' + id);
            };

          var buscarFuncionalidadePorModulo = function(id){
            return $http.get(URL_BASE + '/funcionalidade/pesquisar-por-modulo/' + id);
          };

            var salvarCamposAjuda = function (data) {
                return $http.post(URL_BASE + '/campo-ajuda/salvar',data);
            };

            var pesquisarFuncionalidadeComAjuda = function(query) {
              return $http.get(URL_BASE +'/funcionalidade/pesquisar', {
                params: {
                  idModulo: query.modulo.id,
                  idFuncionalidade: query.funcionalidade.id,
                  numeroPagina: query.numeroPagina,
                  maximoPorPagina: query.maximoPorPagina
                }
              });
            };

            var buscarCamposPorFuncionalidade = function(id){
              return $http.get(URL_BASE + '/campo-ajuda/pesquisar-por-funcionalidade-id/' + id);
            };

            return {
                buscarModulos: buscarModulos,
                buscarFuncionalidadePorModulo: buscarFuncionalidadePorModulo,
                pesquisarFuncionalidadeComAjuda: pesquisarFuncionalidadeComAjuda,
                buscarFuncionalidadePorModuloSemAjuda: buscarFuncionalidadePorModuloSemAjuda,
                buscarCamposPorFuncionalidade: buscarCamposPorFuncionalidade,
                salvarCamposAjuda: salvarCamposAjuda
            };
        }
})();

(function(){
    'use strict';
        angular
        .module('su-integrador')
        .controller('textosAjudaController',
                    ['$scope', '$mdDialog', 'atosContratosService', '$q', 'FormularioFactory', '$filter',
                      'ComumFactory', 'escopoCompartilhadoService', '$state', '$stateParams', 'localStorageService',
                      'instrumentoFuncionalidadeService', 'comumIntegradorService', 'textosAjudaService',
            function ($scope, $mdDialog, atosContratosService, $q, FormularioFactory, $filter, ComumFactory,
                      escopoCompartilhadoService, $state, $stateParams, localStorageService,
                      instrumentoFuncionalidadeService, comumIntegradorService, textosAjudaService) {


          var vm = this;
          vm.isEditar = false;
          var queryDefault = {
            maximoPorPagina: 20,
            numeroPagina:1
          };

          vm.query = angular.copy(queryDefault);

          var init = function(){
            vm.funcionalidades = [];
            textosAjudaService.buscarModulos()
              .then(function(resultado) {
                vm.modulos = resultado.data.resposta;
              });

            if ($stateParams.id) {
              vm.isEditar = true;
              var idFuncionalidade = $stateParams.id;
              textosAjudaService.buscarCamposPorFuncionalidade(idFuncionalidade).then(function(resultado) {
                vm.camposAjuda = resultado.data.resposta;
                vm.query.modulo = vm.camposAjuda[0].funcionalidade.modulo;
                textosAjudaService.buscarFuncionalidadePorModulo(vm.query.modulo.id)
                  .then(function(resultado){
                    vm.funcionalidades = resultado.data.resposta;
                    vm.query.funcionalidade = vm.camposAjuda[0].funcionalidade;
                  });
              });
            } else {
              vm.isEditar = false;
            }
          };

          vm.buscarFuncionalidadePorModulo = function(){
                textosAjudaService.buscarFuncionalidadePorModulo(vm.query.modulo.id)
                            .then(function(resultado){
                        vm.funcionalidades = resultado.data.resposta;
                });
          };

          vm.buscarFuncionalidadePorModuloSemAjuda = function(){
            textosAjudaService.buscarFuncionalidadePorModuloSemAjuda(vm.query.modulo.id)
              .then(function(resultado){
                vm.funcionalidades = resultado.data.resposta;
              });
          };

          vm.limpar = function () {
            vm.query = angular.copy(queryDefault);
          };

          vm.salvarTextosAjuda = function (cadastroTextosAjudaForm) {
            if(cadastroTextosAjudaForm.$invalid){
              cadastroTextosAjudaForm.$setSubmitted();
              ComumFactory.mostrarMensagemError('Necessário preencher os campos obrigatórios');
            }else{
              var campos = {campos: vm.camposAjuda};
              textosAjudaService.salvarCamposAjuda(campos).then(function () {
                cadastroTextosAjudaForm.$setPristine();
                vm.redirecionarTela('INTEGRADOR.TEXTOS_AJUDA');
                ComumFactory.mostrarMensagemSucesso('Dados salvos com sucesso');
              });
            }
          };

          vm.redirecionarTela = function (state) {
            vm.camposAjuda = {};
            $state.go(state);
          };

          vm.irParaEdicao = function (id) {
            $state.go('INTEGRADOR.EDITAR_TEXTOS_AJUDA', {id: id});
          };

          vm.pesquisar = function(){
            if (!vm.query.modulo) {
              vm.query.modulo = {};
            }
            if (!vm.query.funcionalidade) {
              vm.query.funcionalidade = {};
            }
            textosAjudaService.pesquisarFuncionalidadeComAjuda(vm.query)
              .then(function(resultado){
                vm.listaFuncionalidades = resultado.data.content;
                vm.query.totalElements = resultado.data.totalElements;
              });
          };

          vm.pesquisarCamposAjuda =function () {
            textosAjudaService.buscarCamposPorFuncionalidade(vm.query.funcionalidade.id).then(function(resultado) {
              vm.camposAjuda = resultado.data.resposta;
            });
          };

          init();

    }]);
})();

(function() {
	'use strict';
	angular.module('su-integrador')
		.controller('SiapaController',['$scope','$state', '$sce', function ($scope, $state, $sce) {

      if(!$state.params.urlMenu && !$state.params.url){
				$state.go('INTEGRADOR.HOME')	;
			}

			$scope.url = undefined;

			$scope.apresentarFrame = function () {
				if(angular.isUndefined($scope.url)) {
					$scope.url = $sce.trustAsResourceUrl($state.params.url);
				}
				if(angular.isUndefined($scope.url)) {
					$scope.url = $sce.trustAsResourceUrl($state.params.urlMenu);
				}
				return angular.isDefined($scope.url);
			};


		}])
})();

(function () {

  'use strict';

  service.$inject = ["$http"];
  angular
    .module('su-integrador')
    .factory('procedimentoOperacionalPadraoService', service);

  function service ($http) {

    var url = "integrador/api/pop";

    var salvar = function(pop){
      return $http.post(url + "/salvar/", pop);
    };

    var findByTitulo = function(titulo){
      return $http.get(url + "/findByTitulo/" + titulo);
    };

    var findById = function(id){
      return $http.get(url + "/" + id);
    };

    return {
      salvar: salvar,
      findByTitulo: findByTitulo,
      findById: findById
    }

  }

})();

(function () {
  'use strict';
  angular
    .module('su-integrador')
    .controller('procedimentoOperacionalPadraoController',
      ['$scope', 'ComumFactory', 'procedimentoOperacionalPadraoService', 'arquivoServiceIntegrador', '$state', 'integradorEscopoCompartilhadoService',
        function ($scope, ComumFactory, procedimentoOperacionalPadraoService, arquivoServiceIntegrador, $state, integradorEscopoCompartilhadoService) {

          var vm = $scope;

          vm.pop = {
            listArquivoFluxo: [],
            listArquivoAuxiliar: [],
            isRascunho: false
          };

          var init = function(){
            vm.orientacaoTecnica = integradorEscopoCompartilhadoService.getObjeto('orientacaoTecnica');
            vm.indexPop = integradorEscopoCompartilhadoService.getObjeto('indexPop');
            vm.rotaRetorno = integradorEscopoCompartilhadoService.getObjeto('rotaRetorno');
            if(!vm.orientacaoTecnica){
              vm.voltar();
            } else {
              if(vm.indexPop !== undefined){
                if(integradorEscopoCompartilhadoService.getObjeto('consultarPop')){
                  vm.consultar = true;
                } else {
                  vm.editar = true;
                }
              }
              vm.pesquisarPop();
            }
          };

          vm.pesquisarPop = function(){
            if(vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop] && vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop].edited){
              vm.pop = vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop];
            } else {
              if(angular.isUndefined(vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop])){
                return;
              }
              procedimentoOperacionalPadraoService.findById(vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop].id).then(function(resp){
                vm.pop = resp.data.resposta[0];
              });
            }
          };

          vm.setRascunho = function(){
            vm.pop.isRascunho = true;
          };

          vm.uploadArquivoFluxo = function(arquivo) {
            arquivoServiceIntegrador.uploadArquivo(arquivo[0]).then(function(resp){
              vm.pop.listArquivoFluxo.push({id: resp.data.resposta[0], nome: arquivo[0].name});
            });
          };

          vm.uploadArquivoAuxiliar = function(arquivo) {
            arquivoServiceIntegrador.uploadArquivo(arquivo[0]).then(function(resp){
              vm.pop.listArquivoAuxiliar.push({id: resp.data.resposta[0], nome: arquivo[0].name});
            });
          };

          vm.salvar = function(){
            if(!vm.formularioPop.$valid || !vm.pop.procedimento || (vm.pop.isRascunho && !vm.pop.titulo)){
              ComumFactory.mostrarMensagemError("Necessário preencher os campos obrigatórios");
              vm.pop.isRascunho = false;
              return;
            }
            procedimentoOperacionalPadraoService.findByTitulo(vm.pop.titulo).then(function(resp){
              if(resp.data.resposta.length > 0 && !vm.editar){
                ComumFactory.mostrarMensagemError("O Nome/Titulo informado já se encontra cadastrado.");
                vm.pop.isRascunho = false;
              } else {
                if(vm.editar){
                  vm.pop.edited = true;
                  vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop] = vm.pop;
                } else {
                  vm.orientacaoTecnica.listProcedimentoOperacionalPadrao.push(vm.pop);
                }
                integradorEscopoCompartilhadoService.setObjetos('orientacaoTecnica', vm.orientacaoTecnica);
                vm.voltar();
              }
            });
          };

          vm.voltar = function(){
            if(!vm.rotaRetorno){
              $state.go('INTEGRADOR.CONSULTA_ORIENTACAO_TECNICA');
            } else {
              $state.go(vm.rotaRetorno.name, {id: vm.orientacaoTecnica.id});
            }
          };

          init();

        }]);
})();

(function () {

  'use strict';

  service.$inject = ["$http"];
  angular
    .module('su-integrador')
    .factory('orientacaoTecnicaService', service);

  function service ($http) {

    var url = "integrador/api/orientacaoTecnica";
    var urlComum =  "integrador/api/comum";

    var salvar = function(orientacaoTecnica){
      return $http.post(url + "/salvar/", orientacaoTecnica);
    };

    var consultar = function(id){
      return $http.get(url + "/" + id);
    };

    var getTiposOrientacaoTecnica = function(){
      return $http.get(urlComum + "/tipos-orientacao-tecnica/");
    };

    var getAreasOrientacaoTecnica = function(){
      return $http.get(urlComum + "/areas-orientacao-tecnica/");
    };

    var getAssuntosOrientacaoTecnica = function(){
      return $http.get(urlComum + "/assuntos-orientacao-tecnica/");
    };

    return {
      salvar: salvar,
      getTiposOrientacaoTecnica: getTiposOrientacaoTecnica,
      getAreasOrientacaoTecnica: getAreasOrientacaoTecnica,
      getAssuntosOrientacaoTecnica: getAssuntosOrientacaoTecnica,
      consultar: consultar
    }

  }

})();

(function () {
  'use strict';
  angular
    .module('su-integrador')
    .controller('orientacaoTecnicaController',
      ['$scope', 'ComumFactory', '$mdDialog', 'orientacaoTecnicaService', '$state', '$stateParams', 'integradorEscopoCompartilhadoService',
        function ($scope, ComumFactory, $mdDialog, orientacaoTecnicaService, $state, $stateParams, integradorEscopoCompartilhadoService) {

          var vm = $scope;

          vm.orientacaoTecnica = {
            listLinkOrientacaoTecnica: [],
            listProcedimentoOperacionalPadrao: [],
            isRascunho: false
          };
          vm.areasOrientacaoTecnica = [];
          vm.assuntosOrientacaoTecnica = [];
          vm.tiposOrientacaoTecnica = [];

          var init = function(){
            var orientacaoTecnica = integradorEscopoCompartilhadoService.getObjeto('orientacaoTecnica');
            integradorEscopoCompartilhadoService.limparEscopo();
            if(orientacaoTecnica){
              vm.orientacaoTecnica = orientacaoTecnica;
            } else if($stateParams.id){
              orientacaoTecnicaService.consultar($stateParams.id).then(function(resp){
                vm.orientacaoTecnica = resp.data.resposta[0];
              });
            }
            if($state.$current.name === 'INTEGRADOR.ORIENTACAO_TECNICA') {
              $scope.consultar = true;
            }

            orientacaoTecnicaService.getAreasOrientacaoTecnica().then(function(resp){
              vm.areasOrientacaoTecnica = resp.data.resposta;
            });

            orientacaoTecnicaService.getAssuntosOrientacaoTecnica().then(function(resp){
              vm.assuntosOrientacaoTecnica = resp.data.resposta;
            });

            orientacaoTecnicaService.getTiposOrientacaoTecnica().then(function(resp){
              vm.tiposOrientacaoTecnica = resp.data.resposta;
            });

          };

          vm.abrirModalLink = function(index, consultar){
            var link = undefined;
            if(index !== undefined){
              link = vm.orientacaoTecnica.listLinkOrientacaoTecnica[index];
            }

            $mdDialog.show({
              controller: ["$scope", "tiposOrientacaoTecnica", "link", "consultar", function ($scope, tiposOrientacaoTecnica, link, consultar) {
                var init = function(){
                  $scope.consultar = consultar;
                  if(link){
                    $scope.link = angular.copy(link);
                    $scope.link.dateOrientacaoTecnica = new Date($scope.link.dateOrientacaoTecnica);
                    $scope.editar = true;
                  }
                };
                $scope.tiposOrientacaoTecnica = tiposOrientacaoTecnica;
                $scope.adicionarLink = function(){
                  if(!$scope.editar){
                    vm.orientacaoTecnica.listLinkOrientacaoTecnica.push($scope.link);
                  } else {
                    vm.orientacaoTecnica.listLinkOrientacaoTecnica[index] = $scope.link;
                  }
                  $scope.closeDialog();
                };
                $scope.closeDialog = function () {
                  $mdDialog.cancel();
                };
                init();
              }],
              templateUrl: 'scripts-integrador/pages/orientacao-tecnica/view/partials/modalLink.html',
              parent: angular.element(document.body),
              clickOutsideToClose: true,
              locals: {
                tiposOrientacaoTecnica: vm.tiposOrientacaoTecnica,
                link: link,
                consultar: consultar
              }
            });
          };

          vm.deletarLink = function(index){
            var link = vm.orientacaoTecnica.listLinkOrientacaoTecnica[index];
            if(link.id && link.id != null){
              link.isExcluido = true;
            } else {
              vm.orientacaoTecnica.listLinkOrientacaoTecnica.splice(index, 1);
            }
          };

          vm.editarPop = function(index){
            integradorEscopoCompartilhadoService.setObjetos('orientacaoTecnica', vm.orientacaoTecnica);
            integradorEscopoCompartilhadoService.setObjetos('indexPop', index);
            integradorEscopoCompartilhadoService.setObjetos('rotaRetorno', $state.$current);
            $state.go('INTEGRADOR.CADASTRO_PROCEDIMENTO_OPERACIONAL_PADRAO');
          };

          vm.visualizarPop = function(index){
            integradorEscopoCompartilhadoService.setObjetos('orientacaoTecnica', vm.orientacaoTecnica);
            integradorEscopoCompartilhadoService.setObjetos('indexPop', index);
            integradorEscopoCompartilhadoService.setObjetos('consultarPop', true);
            console.log($state.$current)
            integradorEscopoCompartilhadoService.setObjetos('rotaRetorno', $state.$current);
            $state.go('INTEGRADOR.CADASTRO_PROCEDIMENTO_OPERACIONAL_PADRAO');
          };

          vm.deletarPop = function(index){
            var pop = vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[index];
            if(pop.id && pop.id != null){
              pop.isExcluido = true;
            } else {
              vm.orientacaoTecnica.listProcedimentoOperacionalPadrao.splice(index, 1);
            }
          };

          vm.inserirPop = function(){
            integradorEscopoCompartilhadoService.setObjetos('orientacaoTecnica', vm.orientacaoTecnica);
            integradorEscopoCompartilhadoService.setObjetos('rotaRetorno', $state.$current);
            $state.go('INTEGRADOR.CADASTRO_PROCEDIMENTO_OPERACIONAL_PADRAO');
          };

          vm.setRascunho = function(){
            vm.orientacaoTecnica.isRascunho = true;
          };

          vm.salvar = function(){
            if(vm.orientacaoTecnicaForm.$valid){
              orientacaoTecnicaService.salvar(vm.orientacaoTecnica).then(function(resp){
                $state.go('INTEGRADOR.CONSULTA_ORIENTACAO_TECNICA');
              });
            }
          };

          vm.voltar = function(){
            $state.go('INTEGRADOR.CONSULTA_ORIENTACAO_TECNICA');
          };

          init();

        }]);
})();

(function () {

  'use strict';

  service.$inject = ["$http"];
  angular
    .module('su-integrador')
    .factory('instrumentoFuncionalidadeService', service);

  function service ($http) {

    var buscarModulos = function(){
      return $http.get('integrador/api/modulos');
    };

    var buscarTodos = function() {
      return $http.get('integrador/api/instrumento-funcionalidade/pesquisar-todos');
    };

    var buscarFuncionalidadePorModulo = function(id){
      return $http.get('integrador/api/instrumento-funcionalidade/pesquisar-por-modulo/' + id);
    };


    var filterFindAll = function(query) {

      query.modulo =  query.modulo || {};
      query.funcionalidade =  query.funcionalidade || {};
      return $http.get('integrador/api/instumento-funcionalidade-cadastrado/pesquisar-instrumento-funcionalidade/', {
        params: {
          idModulo: query.modulo.id,
          idFuncionalidade: query.funcionalidade.id,
          numeroPagina: query.numeroPagina,
          maximoPorPagina: query.maximoPorPagina
        }
      });
    };

    var desfazer = function () {
      return $http.post('integrador/api/instrumento-funcionalidade/desfazer')
    };

    var salvar = function (funcionalidade) {
      return $http.post('integrador/api/instrumento-funcionalidade/salvar', funcionalidade);
    };

    var salvarFundamentoLegalTemp = function (funcionalidade) {
        return $http.post('integrador/api/fundamento-legal/salvar-fundamento-legal-temp', funcionalidade);
    };

    var buscarFundamentosLegais = function (idFuncionalidade) {
        return $http.get('integrador/api/fundamento-legal/pesquisar-fundamento-legal/'+ idFuncionalidade);
    };

    var buscarFundamentoLegalPorDescricao = function (idFuncionalidade, descricao) {
        return $http.get('integrador/api/fundamento-legal/pesquisar-fundamento-legal-por-descricao',{params:{
          'idFuncionalidade': idFuncionalidade,
          'descricao': descricao
      }});
    };

    var salvarInstrumento = function (funcionalidade) {
      return $http.post('integrador/api/instumento-funcionalidade-cadastrado/salvar', funcionalidade);
    };

    var buscarDadosInstrumento = function (id) {
      return $http.get('integrador/api/instrumento-funcionalidade/pesquisar-instrumento/'+ id);
    };

    var buscarCheckListPorDescricao = function (idFuncionalidade, checkList) {
      return $http.get('integrador/api/checklist/pesquisar-checklist-por-descricao',
        {params:{
          'idFuncionalidade':idFuncionalidade,
          'descricao': checkList.descricao,
          'fundamentoLegalListId': checkList.fundamentoLegalListId
        }});
    };

    var ordenaLista = function (atual, destino, lista) {
      var item = lista[atual];
      lista[atual] = lista[destino];
      lista[destino] = item;
    };

    var buscarInstrumentos = function (idModulo) {
      return $http.get('integrador/api/instrumento-funcionalidade/instrumentos-nao-cadastrados/' + idModulo);
    };

    var suspenderInstrumento = function (instrumento) {
      return $http.post('integrador/api/instumento-funcionalidade-cadastrado/suspender/', instrumento);
    };

    var getUsuario = function () {
      return $http.get('integrador/api/usuario/usuario-logado');
    };

    var buscarInstrumentosCadastrados = function(){
      return $http.get('integrador/api/instumento-funcionalidade-cadastrado/pesquisar-instrumento-funcionalidade');
    };

    var buscarInstrumentosCadastradosAll = function(){
      return $http.get('integrador/api/instumento-funcionalidade-cadastrado/pesquisar-instrumento-funcionalidade-cadastrados');
    };

    return {
      buscarModulos: buscarModulos,
      buscarFuncionalidadePorModulo: buscarFuncionalidadePorModulo,
      filterFindAll : filterFindAll,
      salvar: salvar,
      desfazer: desfazer,
      ordenaLista: ordenaLista,
      buscarFundamentosLegais: buscarFundamentosLegais,
      buscarFundamentoLegalPorDescricao: buscarFundamentoLegalPorDescricao,
      buscarDadosInstrumento: buscarDadosInstrumento,
      salvarFundamentoLegalTemp: salvarFundamentoLegalTemp,
      buscarCheckListPorDescricao: buscarCheckListPorDescricao,
      buscarTodos: buscarTodos,
      salvarInstrumento: salvarInstrumento,
      buscarInstrumentos: buscarInstrumentos,
      suspenderInstrumento: suspenderInstrumento,
      getUsuario: getUsuario,
      buscarInstrumentosCadastrados: buscarInstrumentosCadastrados,
      buscarInstrumentosCadastradosAll: buscarInstrumentosCadastradosAll
    };

  }

})();

/**
 * Created by basis on 19/12/16.
 */
(function(){
  'use strict';
  angular.module('su-integrador')
    .controller('consultaInstrumentoFuncionalidadeController',
      ['$scope','instrumentoFuncionalidadeService','$state','$mdDialog', 'escopoCompartilhadoService','integradorEscopoCompartilhadoService',
        function ($scope, instrumentoFuncionalidadeService, $state, $mdDialog, escopoCompartilhadoService, integradorEscopoCompartilhadoService) {
          var vm = $scope;

          vm.ativo = true;
          vm.paginacao = {
            limit: 5,
            limitsPage: [5, 10, 20],
            page: 1
          };

          vm.suspenderInstrumento = suspenderInstrumento;
          vm.buscarModulos = buscarModulos;
          vm.cadastrarNovoInstrumento = cadastrarNovoInstrumento;
          vm.buscarFuncionalidadePorModulo = buscarFuncionalidadePorModulo;
          vm.limparPesquisa = limparPesquisa;
          vm.filterAll = filterAll;
          vm.vincularFundamentoLegal = vincularFundamentoLegal;
          vm.editarFuncionalidade = editarFuncionalidade;

          function buscarModulos () {
            instrumentoFuncionalidadeService.buscarModulos()
              .then(function (resultado) {
                vm.modulos = resultado.data.resposta;
              });
          }

          function buscarFuncionalidadePorModulo(){
            if(vm.modulo.id){
              instrumentoFuncionalidadeService.buscarFuncionalidadePorModulo(vm.modulo.id)
                .then(function(resultado){
                  vm.funcionalidades = resultado.data.resposta;
                });
            }
          }

          function filterAll() {

            var parametros = {modulo: vm.modulo, funcionalidade: vm.instrumentoFuncionalidadeId, numeroPagina: vm.paginacao.page, maximoPorPagina: vm.paginacao.limit};

            instrumentoFuncionalidadeService.filterFindAll(parametros)
              .then(function(resultado){
                vm.funcionalidadesList = resultado.data.content;
                vm.totalItems = resultado.data.totalElements;
              });

          }

          function cadastrarNovoInstrumento (){
            $state.go('INTEGRADOR.CADASTRAR_INSTRUMENTO_FUNCIONALIDADE');
          }

          function limparPesquisa () {
            vm.modulo = {};
            vm.instrumentoFuncionalidadeId = {};
            vm.funcionalidades = [];
          }

          function suspenderInstrumento(funcionalidade) {
            escopoCompartilhadoService.setObjetos('funcionalidade', funcionalidade);
            $state.go('INTEGRADOR.SUSPENDER_INSTRUMENTO')
          }

          function vincularFundamentoLegal(funcionalidade) {
            vm.isVincular = true;
            vm.instrumentoId =  funcionalidade.instrumentoFuncionalidade.id;
            integradorEscopoCompartilhadoService.setObjetos('idInstrumentoFuncionalidade',vm.instrumentoId);
            integradorEscopoCompartilhadoService.setObjetos('isEditar',vm.isVincular);
            $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
          }

          function editarFuncionalidade(funcionalidade) {
            vm.editar = true;
            escopoCompartilhadoService.setObjetos('editar', vm.editar);
            escopoCompartilhadoService.setObjetos('funcionalidade', funcionalidade);
            $state.go('INTEGRADOR.EDITAR_INSTRUMENTO_FUNCIONALIDADE')
          }



          var init = function(){
            vm.buscarModulos();
            filterAll();
          };

          init();

        }]);
})();


(function () {
  'use strict';
  angular
    .module('su-integrador')
    .controller('cadastroInstrumentoFuncionalidadeController',
      ['$scope', '$mdDialog', 'escopoCompartilhadoService', 'instrumentoFuncionalidadeService', 'ComumFactory', '$stateParams', '$state','$filter','integradorEscopoCompartilhadoService',
        function ($scope, $mdDialog, escopoCompartilhadoService, instrumentoFuncionalidadeService, ComumFactory, $stateParams, $state, $filter,integradorEscopoCompartilhadoService) {

          var vm = $scope;
          var init = function () {
            vm.moduloId = null;
            vm.buscarModulos();


            if(escopoCompartilhadoService.getObjeto('editar') && escopoCompartilhadoService.getObjeto('funcionalidade')){
                vm.instrumento = escopoCompartilhadoService.getObjeto('funcionalidade');
                vm.isEdicao = escopoCompartilhadoService.getObjeto('editar');
                escopoCompartilhadoService.limparEscopo();
                vm.instrumento.modulo = vm.instrumento.instrumentoFuncionalidade.modulo;

                instrumentoFuncionalidadeService.buscarTodos().then(function(resultado) {
                  vm.funcionalidades = resultado.data.resposta;
                });
            }

          };

          vm.instrumento = {
            modulo: {}
          };


          vm.buscarModulos = buscarModulos;
          vm.buscarFuncionalidadePorModulo = buscarFuncionalidadePorModulo;
          vm.formatarString = formatarString;
          vm.verificarInstrumentoPreenchido = verificarInstrumentoPreenchido;
          vm.retornar = retornar;
          vm.salvarInstrumento = salvarInstrumento;
          vm.vincularFundamentoLegal = vincularFundamentoLegal;


          function verificarInstrumentoPreenchido() {
            return vm.instrumento.id;
          }

          function formatarString(item) {
            return ComumFactory.removerHTML(item);
          }

          function retornar() {
            instrumentoFuncionalidadeService.desfazer().then(function () {
              $state.go('INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE');
            })
          }


          function exibirMensagemErro(error) {
            var mensagens = [];
            angular.forEach(error.required, function (field) {
              if (field.$invalid) {
                mensagens.push(field.$name);
              }
            });

            ComumFactory.mostrarMensagemError($filter('translate')('msg-campos-obrigatorios'), mensagens);
          }

          function buscarModulos() {
            instrumentoFuncionalidadeService.buscarModulos()
              .then(function (resultado) {
                vm.modulos = resultado.data.resposta;
              });
          }

          function buscarFuncionalidadePorModulo() {
            if(!vm.isEdicao){
              if (vm.instrumento.modulo) {
                instrumentoFuncionalidadeService.buscarInstrumentos(vm.instrumento.modulo.id)
                  .then(function (resultado) {
                    vm.funcionalidades = resultado.data.resposta;

                  });
              }
            }
          }
          function salvarInstrumento() {
            if(vm.form.$invalid) {
              exibirMensagemErro(vm.form.$error);
              return;
            }

            instrumentoFuncionalidadeService.salvarInstrumento(vm.instrumento).then(function (resp) {
              vm.instrumento.id = resp.data.resposta[0].id;
              ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-documentos'));
              vm.isEdicao = true;

            });
          }

          function vincularFundamentoLegal() {
            vm.isVincular = true;
            vm.instrumentoId =  vm.instrumento.instrumentoFuncionalidade.id;
            integradorEscopoCompartilhadoService.setObjetos('idInstrumentoFuncionalidade',vm.instrumentoId);
            integradorEscopoCompartilhadoService.setObjetos('isEditar',vm.isVincular);
            $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
          }

          init();


        }]);
})();

/**
 * Created by Basis Tecnologia on 03/08/2016.
 */
(function(){
    'use strict';
    angular.module('su-integrador')
        .factory('informesService',['$http', function ($http) {

            var url = '/integrador/api/informes/';

            var findAll = function(inicial, limite){
                return $http.get(url + '?inicial=' + inicial + '&limite=' + limite);
            };

            var findInformesByFilter = function(filtros){
                var endPoint = url + "filtro?titulo="+filtros.titulo;
                if(filtros.dataPublicacaoInicio != null){
                    endPoint = endPoint + "&dataInicio="+filtros.dataPublicacaoInicio;
                }
                if(filtros.dataPublicacaoFim != null){
                    endPoint = endPoint + "&dataFim="+filtros.dataPublicacaoFim;
                }
                return $http.get(endPoint);
            };

            var deletar = function (id) {
                return $http.delete(url + '/' + id);
            };

            var persistir = function (informe) {
                return $http.post(url + 'salvar/', informe);
            };


          var listarInformesHome = function() {
            return $http.get(url + 'home');
          };

            return {
                findAll: findAll,
                findInformesByFilter: findInformesByFilter,
                deletar: deletar,
                persistir: persistir,
                listarInformesHome: listarInformesHome
            };
        }]);
})();

/**
 * Created by Basis Tecnologia on 03/08/2016.
 */
(function(){
    'use strict';
        angular.module('su-integrador')
        .controller('informesController',  ['$scope','$mdDialog','$state', 'informesService','escopoCompartilhadoService',
        function ($scope, $mdDialog,$state, informesService, escopoCompartilhadoService) {

            var vm = $scope;

            vm.listaInformes = [];
            vm.informe = {};

            vm.init = function(){
                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;
                vm.findAll();
                vm.clearFilter();
            };

            vm.findAll = function() {
                informesService.findAll((vm.registroInicial - 1), vm.tamanhoLimite).then(function(retorno){
                    vm.listaInformes = retorno.data.resposta[0].content;
                    vm.totalItems = retorno.data.resposta[0].totalElements;
                });
            };

            vm.findInformesByFilter = function() {
                informesService.findInformesByFilter(vm.filtros).then(function(retorno){
                    vm.listaInformes = retorno.data.resposta;
                    vm.totalItems = vm.listaInformes.length;
                });
            };


            vm.clearFilter = function() {
                vm.filtros = {
                    titulo: "",
                    dataPublicacaoInicio: null,
                    dataPublicacaoFim: null
                };
            };

            vm.showConfirmExcluir = function(ev, id, index) {
                var confirm = $mdDialog.confirm({focusOnOpen:false})
                    .title('Excluir Informe')
                    .textContent('Deseja realmente excluir o informe?')
                    .targetEvent(ev)
                    .ok('Sim')
                    .cancel('Não');
                $mdDialog.show(confirm).then(function() {
                    informesService.deletar(id).then(function(resposta){
                        if(resposta.status == '204')
                            vm.listaInformes.splice(index, 1);
                    });
                }, function() {
                    $mdDialog.cancel();
                });
            };

            vm.redirecionaTela = function(){
                escopoCompartilhadoService.setObjetos('informe',vm.informe);
                $state.go('INTEGRADOR.CADASTRAR_INFORME');
            };

            vm.carregaInformeEdicao = function(informe){
                vm.informe = informe;
                vm.redirecionaTela();
            };

            vm.init();

        }]);
})();

/**
 * Created by Basis Tecnologia on 03/08/2016.
 */
(function(){
    'use strict';
        angular.module('su-integrador')
        .controller('cadastroInformesController',  ['$scope','$mdDialog','$state',
                    'informesService','escopoCompartilhadoService',
                    'arquivoServiceIntegrador','textAngularManager', 'FormularioFactory',
        function ($scope, $mdDialog,$state, informesService, escopoCompartilhadoService,
                  arquivoServiceIntegrador, textAngularManager, FormularioFactory) {

          var vm = $scope;

          vm.SALVAR = 'SALVAR';
          vm.PRE_VISUALIZAR = 'PRE_VISUALIZAR';
          vm.acaoSubmit = '';
          vm.validarFormulario = FormularioFactory.validarFormularioComMensagem;

          vm.informe = {};

          var CONTENT_TYPE_IMAGE = ['image/png', 'image/tif', 'image/jpeg', 'image/jpg'];

          var init = function() {
              vm.informe = escopoCompartilhadoService.getObjeto('informe');
              vm.dataPublicacaoInforme = new Date();
              if (vm.informe) {
                vm.imagem = vm.informe.imagem;
                if (vm.informe.imagem) {
                  vm.imagem.name = vm.informe.imagem.nome;
                  vm.imagem.type = vm.informe.imagem.extencao;
                  vm.imagem.src = arquivoServiceIntegrador.gerarSRCImagemBase64(vm.informe.imagem);
                }
              }
          };

          vm.enviarFormulario = function(){
            if(vm.acaoSubmit == vm.SALVAR){
              gravarDados();
            }else if(vm.acaoSubmit == vm.PRE_VISUALIZAR){
              preVisualizar();
            }
          }

          vm.uploadImagem = function(imagem) {
            if (imagem[0].type && !validarExtensaoImagem(imagem[0].type)) {
                vm.imagem = imagem[0];
                gerarPreviewImagem(imagem[0]);
            }
          };

          var gerarPreviewImagem = function(imagem) {
              arquivoServiceIntegrador.gerarPreviewImagens(imagem, function (src) {
                vm.imagem.src = src;
              });
          };

          var validarExtensaoImagem = function (formato) {
            return CONTENT_TYPE_IMAGE.indexOf(formato) == -1;
          };

          var gravarDados = function() {
              vm.informe.dataPublicacaoInforme = new Date();
              vm.informe.dataPublicacao = null;

              if (vm.imagem) {
                  arquivoServiceIntegrador.uploadArquivo(vm.imagem).then(function (response) {
                    vm.informe.imagem = {
                      id: response.data.resposta[0]
                    };

                    salvarInformes(vm.informe);
                  });
              } else {
                vm.informe.imagem = null;
                salvarInformes(vm.informe);
              }
            };

            var salvarInformes = function(informe) {
                informesService.persistir(informe).then(function (retorno) {
                  $state.go('INTEGRADOR.INFORMES');
                });
            };

            vm.limpar = function() {

                vm.informe = {
                  dataPublicacaoInforme: new Date()
                };

                vm.informe.titulo = undefined;
                vm.informe.corpo = undefined;
                vm.informe.dataPublicacaoInforme = new Date();
                vm.informe.dataPublicacao = undefined;
                vm.informe.imagem = undefined;
                vm.imagem = undefined;
            };

            var preVisualizar = function(ev){
                $mdDialog.show({
                    controller: ["$scope", "$mdDialog", "informe", "imagem", "dataPublicacaoInforme", "$sce", function ($scope, $mdDialog, informe, imagem, dataPublicacaoInforme, $sce) {
                      $scope.informe = informe;
                      $scope.imagem = imagem;
                      $scope.dataPublicacaoInforme = dataPublicacaoInforme;

                      $scope.fechar = function () {
                        $mdDialog.cancel();
                      };

                      $scope.renderCorpoHtml = function(){
                        return $sce.trustAsHtml($scope.informe.corpo);
                      };

                    }],
                    templateUrl: 'scripts-integrador/pages/informes/view/dialog/preVisualizar.html',
                    parent: angular.element(document.body),
                    targetEvent: ev,
                    clickOutsideToClose: true,
                    locals: {
                        informe: vm.informe,
                        imagem: vm.imagem,
                        dataPublicacaoInforme: vm.dataPublicacaoInforme
                }
                });
            };

          init();
        }]);
})();

/**
 * Created by Basis Tecnologia on 13/07/2016.
 */
(function(){
    'use strict';

    PendenciaService.$inject = ["$http"];
    angular.module('su-integrador').factory('PendenciaService', PendenciaService);

    function PendenciaService($http){
        var url = '/integrador/api/dashboard/pendencia';

        var findPendenciaServicos = function(){
           return $http.get(url + "/servicos");
        };

        var findPendenciaCadastroImovel = function(){
            return $http.get(url + "/cadastro-imovel");
        };

        var findPendenciaAvaliacaoContabilidade = function(){
            return $http.get(url + "/avaliacao");
        };

        var findPendenciaDestinacao = function(){
            return $http.get(url + "/destinacao");
        };

        var findPendenciaReceitas = function(){
            return $http.get(url + "/receita");
        };

        var findPendenciaFiscalizacao = function(){
            return $http.get(url + "/fiscalizacao");
        };

        var findPendenciaGeoInformacoes = function(){
            return $http.get(url + "/geoinformacao");
        };

        return {
                findPendenciaServicos : findPendenciaServicos,
                findPendenciaCadastroImovel : findPendenciaCadastroImovel,
                findPendenciaAvaliacaoContabilidade: findPendenciaAvaliacaoContabilidade,
                findPendenciaDestinacao: findPendenciaDestinacao,
                findPendenciaReceitas:findPendenciaReceitas,
                findPendenciaFiscalizacao:findPendenciaFiscalizacao,
                findPendenciaGeoInformacoes:findPendenciaGeoInformacoes
        };
    }
})();

/**
 * Created by Basis Tecnologia on 12/08/2016.
 */
/**
 * Created by Basis Tecnologia on 13/07/2016.
 */
(function(){
    'use strict';

    PainelServicoService.$inject = ["$http"];
    angular.module('su-integrador').factory('PainelServicoService', PainelServicoService);

    function PainelServicoService($http){
        var url = '/integrador/api/posicaoCarteira';

        var getDadosPainel = function(){
            return $http.get(url);
        };

        var getPercentualPendentes = function(dadosPainelServico){
            return parseInt((dadosPainelServico.totalPendentes / dadosPainelServico.totalRequerimentos) * 100);
        };


        return {
            getDadosPainel: getDadosPainel,
            getPercentualPendentes: getPercentualPendentes
        };
    }
})();

/**
 * Created by Basis Tecnologia on 14/07/2016.
 */
(function () {
    'use strict';

    MensagemService.$inject = ["$http"];
    angular.module('su-integrador').factory('MensagemService', MensagemService);

    function MensagemService($http) {

        var url = '/integrador/api/dashboard/mensagem';

        var findMensagensServicos = function () {
            return $http.get(url + "/servicos");
        };

        var findMensagensCadastroImovel = function(){
            return [];
        };

        var findMensagensAvaliacaoContabilidade = function(){
            return [];
        };

        var findMensagensDestinacao = function(){
            return [];
        };

        var findMensagensReceitas = function(){
            return [];
        };

        var findMensagensFiscalizacao = function(){
            return [];
        };

        var findMensagensGeoInformacoes = function(){
            return [];
        };

        var alterarMensagemParaLida = function (idMensagem) {
            return $http.get(url + "/mensagemLida/" + idMensagem);
        };

        return{
            findMensagensServicos : findMensagensServicos,
            findMensagensCadastroImovel : findMensagensCadastroImovel,
            findMensagensAvaliacaoContabilidade : findMensagensAvaliacaoContabilidade,
            findMensagensDestinacao : findMensagensDestinacao,
            findMensagensReceitas : findMensagensReceitas,
            findMensagensFiscalizacao : findMensagensFiscalizacao,
            findMensagensGeoInformacoes : findMensagensGeoInformacoes,
            alterarMensagemParaLida : alterarMensagemParaLida
        }
    }
})();

/**
 * Created by Basis Tecnologia on 12/08/2016.
 */
/**
 * Created by Basis Tecnologia on 13/07/2016.
 */
(function(){
    'use strict';

    QuadroResumoService.$inject = ["$http"];
    angular.module('su-integrador').factory('QuadroResumoService', QuadroResumoService);

    function QuadroResumoService($http){
        var url = '/integrador/api/quadroResumo';

        var findAllQuadroResumo = function(){
            return $http.get(url);
        };

        var montarGrafico = function(titulo, dadosGrafico){
            var opcoesGrafico = {
                options: {
                    chart: {
                        type: 'pie',
                        plotBackgroundColor: null,
                        plotBorderWidth: null,
                        plotShadow: false,
                        width: 280

                    },
                    tooltip: {
                        pointFormat: '{point.key}: <b>{point.y}</b>',
                        headerFormat: ''
                    },
                    legend: {
                        labelFormat: '{key}'
                    },
                    plotOptions: {
                        pie: {
                            dataLabels: {
                                enabled: true,
                                format: '{point.percentage:.1f} %'
                            },
                            showInLegend: true
                        }

                    }

                },

                title: {
                    text: titulo
                },
                colorByPoint: true,
                series: [{
                    type: 'pie',
                    data: dadosGrafico
                }],


                loading: false
            };
            return opcoesGrafico;
        };

        return {
            findAllQuadroResumo: findAllQuadroResumo,
            montarGrafico: montarGrafico
        };
    }
})();

(function () {
  'use strict';
  angular.module('su-integrador')
    .controller('HomeIntegradorController', ['$scope', '$mdDialog', '$mdToast', 'PendenciaService', 'MensagemService', 'QuadroResumoService', 'PainelServicoService', 'ComumFactory', '$sce', 'informesService', 'arquivoServiceIntegrador', 'geovisIntegradorFactory','$timeout','$state',
      function ($scope, $mdDialog, $mdToast, PendenciaService, MensagemService, QuadroResumoService, PainelServicoService, ComumFactory, $sce, informesService, arquivoServiceIntegrador, geovisIntegradorFactory, $timeout, $state) {

        var self = this;
        self.teste = {};
        self.pendencias = [];
        self.tituloPendencia = "";
        $scope.urlServicos;
        $scope.urlDestinacao;
        // comumService.buscarUrlsMenusPorAmbiente().then(function (response) {
        //   var url = response.data;
        //   $scope.urlServicos = $sce.trustAsResourceUrl(url.servicos+'/#/servicos/triagem?status=AGUARDANDO_ANALISE_PREVIA');
        //   $scope.urlDestinacao = $sce.trustAsResourceUrl(url.urlDestinacao+'/#/doacao');
        // });

        /**
         * PENDÊNCIAS
         * */
        // PendenciaService.findPendenciaServicos().then(function(retorno){
        //   self.pendenciasServicos = retorno.data;
        // });
        PendenciaService.findPendenciaCadastroImovel().then(function(retorno){
          self.pendenciasCadastroImovel = retorno.data;
        });
        PendenciaService.findPendenciaAvaliacaoContabilidade().then(function(retorno){
          self.pendenciasAvaliacaoContabilidade = retorno.data;
        });
        // PendenciaService.findPendenciaDestinacao().then(function(retorno){
        //   self.pendenciasDestinacao = retorno.data;
        // });
        // PendenciaService.findPendenciaReceitas().then(function(retorno){
        //   self.pendenciasReceitas = retorno.data;
        // });
        // PendenciaService.findPendenciaFiscalizacao().then(function(retorno){
        //   self.pendenciasFiscalizacao = retorno.data;
        // });
        // PendenciaService.findPendenciaGeoInformacoes().then(function(retorno){
        //   self.pendenciasGeoInformacoes = retorno.data;
        // });


        /**
         * Dialog de pendências
         * */
        self.showPendenciasDialog = function (ev,tipoPendencia) {
          atribuiTipoPendencia(tipoPendencia);
          if(self.pendencias.length > 0 ){
            $mdDialog.show({
              controller: pendenciasDialogController,
              templateUrl: 'scripts-integrador/pages/home/view/dialogs/pendencias.dialog.html',
              parent: angular.element(document.body),
              targetEvent: ev,
              clickOutsideToClose: true,
              locals: {
                pendencias: self.pendencias,
                tituloPendencia: self.tituloPendencia
              }
            });
          }
        };

        var pendenciasDialogController = function ($scope, $mdDialog, pendencias, tituloPendencia) {
          $scope.pendencias = pendencias;
          $scope.tituloPendencia = tituloPendencia;

          $scope.abrirPendencia = function (pendencia) {
            window.sessionStorage.setItem('pendencia', JSON.stringify(pendencia));
          };

          $scope.fechar = function () {
            $mdDialog.cancel();
          };
        };
        pendenciasDialogController.$inject = ["$scope", "$mdDialog", "pendencias", "tituloPendencia"];

        var atribuiTipoPendencia = function(tipoPendencia){
          // desabilitando pesquisa de serviços pendentes
          // if(tipoPendencia === 1){
          //   self.pendencias = self.pendenciasServicos.pendencias;
          //   self.tituloPendencia = "Lista de Pendências - Serviços";
          // }
          if(tipoPendencia === 1){
            self.pendencias = [];
            self.tituloPendencia = "Lista de Pendências - Serviços";
          }
          if(tipoPendencia === 2){
            self.pendencias = self.pendenciasCadastroImovel.pendencias;
            self.tituloPendencia = "Lista de Pendências - Cadastro de imóveis";
          }
          if(tipoPendencia === 3){
            self.pendencias = self.pendenciasAvaliacaoContabilidade.pendencias;
            self.tituloPendencia = "Lista de Pendências - Avaliação/Contabilidade";
          }
          if(tipoPendencia === 4){
            self.pendencias = self.pendenciasDestinacao.pendencias;
            self.tituloPendencia = "Lista de Pendências - Destinação";
          }
          if(tipoPendencia === 5){
            self.pendencias = [];
            self.tituloPendencia = "Lista de Pendências - Receitas";
          }
          if(tipoPendencia === 6){
            self.pendencias = [];
            self.tituloPendencia = "Lista de Pendências - Fistalização";
          }
          if(tipoPendencia === 7){
            self.pendencias = [];
            self.tituloPendencia = "Lista de Pendências - Geo Informações";
          }
        };

        /**
         *	MENSAGENS
         */
        /** Comentado até definição da funcionalidade
         *
         MensagemService.findMensagensServicos().then(function(retorno){
				self.mensagens = retorno.data.mensagens;
				self.quantidadeMensagensNaoLidas = retorno.data.quantidadeMensagensNaoLidas;
			});
         self.mensagemSelecionada = {};

         self.mensagemLida = function (indexMensagem) {
				self.mensagemSelecionada = self.mensagens[indexMensagem];
				if (self.mensagemSelecionada.lida == false) {
					MensagemService.alterarMensagemParaLida(self.mensagemSelecionada.id).then(function(retorno){
						self.mensagemSelecionada.lida = retorno.data.lida;
						self.quantidadeMensagensNaoLidas -= 1;
					});
				}
			};


         self.showMensagemDialog = function (ev, indexMensagem) {
				$mdDialog.show({
					controller: mesagemDialogController,
					templateUrl: 'scripts-integrador/pages/home/view/dialogs/mensagem.dialog.html',
					parent: angular.element(document.body),
					targetEvent: ev,
					clickOutsideToClose: true,
					locals: {
						mensagemSelecionada: self.mensagens[indexMensagem]
					}
				});
			};

         var mesagemDialogController = function ($scope, $mdDialog, mensagemSelecionada) {
				$scope.mensagemSelecionada = mensagemSelecionada;

				$scope.fechar = function () {
					$mdDialog.cancel();
				};

				$scope.voltarParaDlgMensagens = function (ev) {
					$mdDialog.cancel();
					self.showMensagensDialog(ev);
				}
			};




         self.showMensagensDialog = function (ev) {
				$mdDialog.show({
					controller: mesagensDialogController,
					templateUrl: 'scripts-integrador/pages/home/view/dialogs/mensagens.dialog.html',
					parent: angular.element(document.body),
					targetEvent: ev,
					clickOutsideToClose: true,
					locals: {
						mensagens: self.mensagens
					}
				});
			};

         var mesagensDialogController = function ($scope, $mdDialog, mensagens) {
				$scope.mensagens = mensagens;

				$scope.fechar = function () {
					$mdDialog.cancel();
				};

				$scope.mensagemLida = function(indexMensagem){
					self.mensagemLida(indexMensagem);
				};

				$scope.exibirMensagem = function(ev, indexMensagem){
					$mdDialog.cancel();
					self.showMensagemDialog(ev, indexMensagem);
				};
			};

         */


        /*FUNCOES FAVORITAS*/

        self.redirecionaFuncaoFavorita = function () {
          alert("Redirecionando para função favorita....");
        };

        /*INFORMES*/

        var initInformes = function() {

          self.tamanhoLimite = 5;
          self.totalItems = 0;
          self.registroInicial = 1;

          self.informeSelecionado = {};
          self.findTodosInformes();

          informesService.listarInformesHome().then(function (response) {
            self.informesHome = response.data.resposta;
          });
        };

        self.findTodosInformes = function() {
          informesService.findAll((self.registroInicial - 1), self.tamanhoLimite).then(function(response) {
            self.informes = response.data.resposta[0].content;
            self.totalItems = response.data.resposta[0].totalElements;
          });
        };

        self.gerarSrcImagem = function(imagem) {
          return arquivoServiceIntegrador.gerarSRCImagemBase64(imagem);
        };

        self.resetInformeSelecionado = function () {
          self.informeSelecionado = {};
        };

        self.continuarLendo = function (informe) {
          self.informeSelecionado = informe;
        };

        self.showInformesDialog = function (ev) {
          $mdDialog.show({
            controller: informeDialogController,
            templateUrl: 'scripts-integrador/pages/home/view/dialogs/informe.dialog.html',
            parent: angular.element(document.body),
            targetEvent: ev,
            clickOutsideToClose: true,
            locals: {
              informes: self.informes,
              informeSelecionado: self.informeSelecionado,
              tamanhoLimite: self.tamanhoLimite,
              totalItems: self.totalItems,
              registroInicial: self.registroInicial,
              findTodosInformes: self.findTodosInformes
            }
          });
        };

        var informeDialogController = function ($scope, $mdDialog, informes, informeSelecionado,
                                                tamanhoLimite, totalItems, registroInicial, findTodosInformes, ComumFactory, arquivoServiceIntegrador, $sce) {
          $scope.informes = informes;
          $scope.informeSelecionado = informeSelecionado;

          $scope.tamanhoLimite = tamanhoLimite;
          $scope.totalItems = totalItems;
          $scope.registroInicial = registroInicial;

          $scope.findTodosInformes = findTodosInformes;

          $scope.fechar = function () {
            $mdDialog.cancel();
          };

          $scope.descricaoExtensa = function (descricaoInforme) {
            descricaoInforme = ComumFactory.removerHTML(descricaoInforme);

            if (descricaoInforme.length > 200) {
              descricaoInforme = descricaoInforme.substring(0, 200);
              descricaoInforme += "...";
              return descricaoInforme;
            } else {
              return descricaoInforme;
            }
          };

          $scope.gerarSrcImagem = function(imagem) {
            return arquivoServiceIntegrador.gerarSRCImagemBase64(imagem);
          };

          $scope.controleExibicao = true;
          $scope.exibirDescricao = function (informe) {
            $scope.informeSelecionado = informe;
            $scope.controleExibicao = false;
          };

          if (informeSelecionado.id !== undefined) {
            $scope.descricao = $sce.trustAsHtml(informeSelecionado.descricao);
            $scope.controleExibicao = false;
          }

          $scope.voltar = function () {
            $scope.informeSelecionado = {};
            $scope.controleExibicao = true;
          }

        };
        informeDialogController.$inject = ["$scope", "$mdDialog", "informes", "informeSelecionado", "tamanhoLimite", "totalItems", "registroInicial", "findTodosInformes", "ComumFactory", "arquivoServiceIntegrador", "$sce"];

        self.descricaoExtensa = function (descricaoInforme) {
          descricaoInforme = ComumFactory.removerHTML(descricaoInforme);

          if (descricaoInforme.length > 200) {
            descricaoInforme = descricaoInforme.substring(0, 120);
            descricaoInforme += "...";
            return descricaoInforme;
          } else {
            return descricaoInforme;
          }
        };

        initInformes();

        /**
         * Gráficos
         * */

        self.dadosGraficoQuantidade = [];
        self.dadosGraficoArea = [];
        self.dadosGraficoValorImovel = [];

        QuadroResumoService.findAllQuadroResumo().then(function(retorno){
          for(var i = 0; i < retorno.data.resposta.length; i++){
            var dadosQuantidade ={};
            dadosQuantidade.key = retorno.data.resposta[i].tipoInformacao;
            dadosQuantidade.y = retorno.data.resposta[i].quantidade;
            self.dadosGraficoQuantidade.push(dadosQuantidade);

            var dadosArea ={};
            dadosArea.key = retorno.data.resposta[i].tipoInformacao;
            dadosArea.y = retorno.data.resposta[i].valorAreaSoma;
            self.dadosGraficoArea.push(dadosArea);

            var dadosValorImovel ={};
            dadosValorImovel.key = retorno.data.resposta[i].tipoInformacao;
            dadosValorImovel.y = retorno.data.resposta[i].valorImovelSoma;
            self.dadosGraficoValorImovel.push(dadosValorImovel);


          }
          self.opcoesGraficoQuantidade = QuadroResumoService.montarGrafico('Quantidade',self.dadosGraficoQuantidade);
          self.opcoesGraficoArea = QuadroResumoService.montarGrafico('Área',self.dadosGraficoArea);
          self.opcoesGraficoValor = QuadroResumoService.montarGrafico('Valor',self.dadosGraficoValorImovel);

        });


        /**
         * Painel de Serviços
         *
         */
        self.dadosPainelServico = {};
        PainelServicoService.getDadosPainel().then(function(retorno){
          console.log(retorno.data.resposta);
          self.dadosPainelServico = retorno.data.resposta[0];
          self.dadosPainelServico.percentualPendentes = PainelServicoService.getPercentualPendentes(self.dadosPainelServico);
        });

        $timeout(function () {
            geovisIntegradorFactory.criaMapa();
        }, 1000);

        window.addEventListener("resize", function(event) {
          if (!!document.getElementById('homeMapGeovis')) {
            document.getElementById('homeMapGeovis').innerHTML = "";
            geovisIntegradorFactory.criaMapa();
          }
        })

        self.geovisPesquisar = function (){
            if(self.buscaGeovis != undefined){
                window.open("http://spunet-geovis.2.nuvem.gov.br/#/geovisualizador?busca="+self.buscaGeovis, "_blank");
            }
        }


        self.videos = [
          {
            image: 'assets/img/suporte.png'
          },
          {
            image: 'assets/img/brasil.png'
          }
        ];

      }])
})();

(function () {
    'use strict';
    angular
      .module('su-integrador')
      .factory('geovisIntegradorFactory', function () {
            
         var view;
         
         function criaMapa () {
                 
             var controls = ol.control.defaults({zoom: false});
             controls.extend([new ol.control.ScaleLine()]);
             var interactions = ol.interaction.defaults();
             
             var homeMapGeovis = new ol.Map({
              target: 'homeMapGeovis',
                  controls: controls,
                  interactions: interactions,
              view: new ol.View({
                projection: 'EPSG:4326',
                center: [-51.4198237555309, -14.2364826698601],
                zoom: 4.54
              })
             });

             var layerBase = new ol.layer.Tile({
                 source: new ol.source.OSM()
             });
             homeMapGeovis.addLayer(layerBase);
             view = homeMapGeovis.getView();
         }
        return {
            criaMapa: criaMapa
        };
});
})();



(function () {

  'use strict';

  service.$inject = ["$http"];
  angular
    .module('su-integrador')
    .factory('fundamentoLegalService', service);

  function service ($http) {

    var URL_FUNDAMENTO_LEGAL = 'integrador/api/fundamento-legal';

    var buscarCriteriosUtilizado = function(){
      return $http.get(URL_FUNDAMENTO_LEGAL + '/buscar-criterios-utilizado');
    };

    var buscarNaturezasPermitidas = function() {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/buscar-natureza-juridica');
    };

    var salvar = function(fundamentoLegal) {
      return $http.post(URL_FUNDAMENTO_LEGAL, fundamentoLegal);
    };

    var cancelar = function (fundamentoLegal) {
      return $http.post(URL_FUNDAMENTO_LEGAL + '/cancelar', fundamentoLegal);
    };

    var findDadosCancelamento = function (id) {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/fundamento-cancelado', {
        params:{
          id:id
        }
      })
    };

    var buscarFundamentosLegais = function(filtroConsulta, offset, limit) {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/consulta-paginacao', {
        params: {
          idInstrumentoFuncionalidade: filtroConsulta.idInstrumentoFuncionalidade,
          template: filtroConsulta.template,
          offset: offset,
          limit: limit
        }
      });
    };

    var pesquisarTodosFundamentos = function() {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/find-all');
    };

    var pesquisarTodosTiposIncorporacao = function() {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/tipo-incorporacao/all');
    };

    var pesquisarTodosTiposAquisicao = function() {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/tipo-aquisicao/all');
    };

    var pesquisarTodasEntidadesExtintas = function() {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/entidade-extinta/all');
    };

    var pesquisarTodosTipoUsoPermitido = function () {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/tipo-uso-permitido/all');
    };

    var pesquisarEspecificacao = function (utilizacao) {
      return $http.get(URL_FUNDAMENTO_LEGAL +'/buscar-especificacao?utilizacao=' + utilizacao);
    };

    var buscarTodasEspecificacoes  = function (utilizacao) {
      return $http.get(URL_FUNDAMENTO_LEGAL +'/especificacoes/all/');
    };

    var findById = function (id) {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/find-by-id',{
        params: {
          id: id
        }
      });
    };

    var buscarTiposAvaliacao = function () {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/buscar-tipo-avaliacao');
    };

    return {
      buscarCriteriosUtilizado: buscarCriteriosUtilizado,
      buscarNaturezasPermitidas: buscarNaturezasPermitidas,
      salvar: salvar,
      buscarFundamentosLegais: buscarFundamentosLegais,
      pesquisarTodosFundamentos: pesquisarTodosFundamentos,
      pesquisarTodosTiposIncorporacao: pesquisarTodosTiposIncorporacao,
      pesquisarTodosTiposAquisicao: pesquisarTodosTiposAquisicao,
      pesquisarTodasEntidadesExtintas: pesquisarTodasEntidadesExtintas,
      pesquisarTodosTipoUsoPermitido: pesquisarTodosTipoUsoPermitido,
      pesquisarEspecificacao: pesquisarEspecificacao,
      buscarTodasEspecificacoes: buscarTodasEspecificacoes,
      cancelar: cancelar,
      findById: findById,
      findDadosCancelamento:findDadosCancelamento,
      buscarTiposAvaliacao:buscarTiposAvaliacao
    };

  }

})();

(function(){
  'use strict';

  angular.module('su-integrador')
    .controller('consultarFundamentoLegalController',
      ["$scope", "instrumentoFuncionalidadeService", "$state", "fundamentoLegalService", "integradorEscopoCompartilhadoService", function ($scope, instrumentoFuncionalidadeService, $state, fundamentoLegalService, integradorEscopoCompartilhadoService) {

        var vm = $scope;

        vm.listaInstrumentosFuncionalidade = [];
        vm.filtro = {};

        vm.listaFundamentosLegais = [];

        var init = function() {
          vm.registroInicial = 1;
          vm.tamanhoLimite = 5;
          vm.totalItems = 0;

          if (integradorEscopoCompartilhadoService.getObjeto('idInstrumentoFuncionalidade')
                && integradorEscopoCompartilhadoService.getObjeto('isEditar')) {
            vm.filtro.idInstrumentoFuncionalidade = integradorEscopoCompartilhadoService.getObjeto('idInstrumentoFuncionalidade');
            vm.isEditar = integradorEscopoCompartilhadoService.getObjeto('isEditar');
            integradorEscopoCompartilhadoService.limparEscopo();
          }

          buscarInstrumentosFuncionalidade();
          vm.pesquisarFundamentos();
        };

        var buscarInstrumentosFuncionalidade = function() {
          instrumentoFuncionalidadeService.buscarInstrumentosCadastradosAll().then(function(resp) {
            vm.listaInstrumentosFuncionalidade = resp.data.resposta;
          });
        };

        vm.redirecionarEditarFundamentoLegal = function (fundamento) {
          vm.isEditar = true;
          integradorEscopoCompartilhadoService.setObjetos('fundamentoLegal', fundamento);
          integradorEscopoCompartilhadoService.setObjetos('idInstrumentoFuncionalidade',fundamento.idInstrumento);
          integradorEscopoCompartilhadoService.setObjetos('isEditar',vm.isEditar);
          $state.go('INTEGRADOR.EDITAR_FUNDAMNETO_LEGAL');
        };

        vm.cancelarFundamentoLegal = function (fundamento) {
          integradorEscopoCompartilhadoService.setObjetos('fundamentoLegal', fundamento);
          $state.go('INTEGRADOR.CANCELAR_FUNDAMENTO_LEGAL');
        };

        vm.detalharFundamentoLegal = function (fundamento) {
          vm.isDetalhar = true;
          integradorEscopoCompartilhadoService.setObjetos('fundamentoLegal', fundamento);
          integradorEscopoCompartilhadoService.setObjetos('idInstrumentoFuncionalidade',fundamento.idInstrumento);
          integradorEscopoCompartilhadoService.setObjetos('isDetalhar',vm.isDetalhar);
          $state.go('INTEGRADOR.DETALHAR_FUNDAMENTO_LEGAL');
        };

        vm.voltar = function () {
          $state.go('INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE');
        };

        vm.limparFiltroConsulta = function() {
          vm.filtro = {};
          vm.filtro.idInstrumentoFuncionalidade = null;
        };

        vm.pesquisarFundamentos = function () {
          fundamentoLegalService.buscarFundamentosLegais(vm.filtro, (vm.registroInicial - 1), vm.tamanhoLimite).then(function(resp) {
            vm.listaFundamentosLegais = resp.data.resposta[0].content;
            vm.totalItems = resp.data.resposta[0].totalElements;
          });
        };

        vm.cadastrarFundamento = function () {
          if(vm.isEditar){
            integradorEscopoCompartilhadoService.setObjetos('idInstrumentoFuncionalidade',vm.filtro.idInstrumentoFuncionalidade);
            integradorEscopoCompartilhadoService.setObjetos('isEditar',vm.isEditar);
          }
          $state.go('INTEGRADOR.CADASTRAR_FUNDAMENTO_LEGAL');
        };

        init();
      }]);
})();

(function(){
  'use strict';

  angular.module('su-integrador')
    .controller('cadastrarFundamentoLegalController',
      ["$scope", "$filter", "instrumentoFuncionalidadeService", "fundamentoLegalService", "$state", "ComumFactory", "integradorEscopoCompartilhadoService", function ($scope, $filter, instrumentoFuncionalidadeService, fundamentoLegalService, $state, ComumFactory, integradorEscopoCompartilhadoService) {

        var vm = $scope;

        vm.fundamentoLegal = {naturezaJuridicaPermitidas: []};
        vm.bloquear = false;
        vm.valorPercentual = false;

        vm.listaInstrumentosFuncionalidade = [];
        vm.listaTiposAvaliacao = [];
        vm.listaCriterios = [];
        vm.listaNaturezasPermitidas = [];
        vm.listaFundamentosCadastrados = [];
        vm.listaIncorporacao = [];
        vm.listaAquisicao = [];
        vm.listaEntidadesExtintas = [];

        vm.registroInicial = 1;
        vm.tamanhoLimite = 5;
        vm.totalItems = 0;
        vm.paginationFundamentosLegaisTipoAvaliacao = [];

        vm.fundamentoLegalTipoAvaliacao = {};
        vm.fundamentoLegal.tipoAvaliacao = null;
        vm.fundamentoLegal.metodologiaAvaliacao = null;

        var TODAS = 'TODAS';
        var NORTE = 'NORTE';
        var NORDESTE = 'NORDESTE';
        var CENTRO_OESTE = 'CENTRO_OESTE';
        var SUDESTE = 'SUDESTE';
        var SUL = 'SUL';
        var URBANO = 'URBANO';
        var RURAL = 'RURAL';
        var edit = false;

        vm.DESTINACAO = 'DESTINACAO';
        vm.moduloSelecionado = null;

        var configuracoesPadrao = {
          regiao: '',
          regiaoLabel: '',
          natureza: '',
          naturezaLabel: '',
          isSuperintendente: true,
          isSecretario: true,
          isMinistro: true,
          minimoSuperintendente: 0,
          maximoSuperintendente: null,
          minimoSecretario: null,
          maximoSecretario: null,
          minimoMinistro: null,
          maximoMinistro: null,
          isDiferencaRegiao: false,
          isDiferencaNatureza: false
        };

        var todasLabel = $filter('translate')('label-todas');
        var regioesPadrao = {norte: NORTE, nordeste: NORDESTE, centrooeste: CENTRO_OESTE, sudeste: SUDESTE, sul: SUL};
        var regioesLabelPadrao = {norte: $filter('translate')('label-norte'), nordeste: $filter('translate')('label-nordeste'), centrooeste: $filter('translate')('label-centro-oeste'), sudeste: $filter('translate')('label-sudeste'), sul: $filter                ('translate')('label-sul')};
        var naturezasPadrao = {urbano: URBANO, rural: RURAL};
        var naturezasLabelPadrao = {urbano: $filter('translate')('label-urbano'), rural: $filter('translate')('label-rural')};

        vm.AREA_IMOVEL_PARCELA = 'AREA_IMOVEL_PARCELA';
        vm.VALOR_IMOVEL_PARCELA = 'VALOR_IMOVEL_PARCELA';

        var init = function() {
          vm.configuracoesTodasTodas = [
            definirConfiguracaoFundamentoLegal(TODAS, todasLabel, TODAS, todasLabel, false, false)
          ];
          vm.configuracoesTodasNatureza = [
            definirConfiguracaoFundamentoLegal(TODAS, todasLabel, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, false, true),
            definirConfiguracaoFundamentoLegal(TODAS, todasLabel, naturezasPadrao.rural, naturezasLabelPadrao.rural, false, true)
          ];
          vm.configuracoesRegiaoNatureza = [
            definirConfiguracaoFundamentoLegal(regioesPadrao.norte, regioesLabelPadrao.norte, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.norte, regioesLabelPadrao.norte, naturezasPadrao.rural, naturezasLabelPadrao.rural, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.nordeste, regioesLabelPadrao.nordeste, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.nordeste, regioesLabelPadrao.nordeste, naturezasPadrao.rural, naturezasLabelPadrao.rural, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.centrooeste, regioesLabelPadrao.centrooeste, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.centrooeste, regioesLabelPadrao.centrooeste, naturezasPadrao.rural, naturezasLabelPadrao.rural, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sudeste, regioesLabelPadrao.sudeste, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sudeste, regioesLabelPadrao.sudeste, naturezasPadrao.rural, naturezasLabelPadrao.rural, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sul, regioesLabelPadrao.sul, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sul, regioesLabelPadrao.sul, naturezasPadrao.rural, naturezasLabelPadrao.rural, true, true)
          ];
          vm.configuracoesRegiaoTodas = [
            definirConfiguracaoFundamentoLegal(regioesPadrao.norte, regioesLabelPadrao.norte, TODAS, todasLabel, true, false),
            definirConfiguracaoFundamentoLegal(regioesPadrao.nordeste, regioesLabelPadrao.nordeste, TODAS, todasLabel, true, false),
            definirConfiguracaoFundamentoLegal(regioesPadrao.centrooeste, regioesLabelPadrao.centrooeste, TODAS, todasLabel, true, false),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sudeste, regioesLabelPadrao.sudeste, TODAS, todasLabel, true, false),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sul, regioesLabelPadrao.sul, TODAS, todasLabel, true, false),
          ];

          if (integradorEscopoCompartilhadoService.getObjeto('idInstrumentoFuncionalidade')
            && (integradorEscopoCompartilhadoService.getObjeto('isEditar') || integradorEscopoCompartilhadoService.getObjeto('isDetalhar'))) {
            vm.fundamentoLegal.idInstrumentoFuncionalidade = integradorEscopoCompartilhadoService.getObjeto('idInstrumentoFuncionalidade');
            vm.isEditar = integradorEscopoCompartilhadoService.getObjeto('isEditar');
            vm.isDetalhar = integradorEscopoCompartilhadoService.getObjeto('isDetalhar');


            if(integradorEscopoCompartilhadoService.getObjeto('fundamentoLegal')){

              vm.fundamentoLegal = integradorEscopoCompartilhadoService.getObjeto('fundamentoLegal');
              edit = true;
              fundamentoLegalService.findById(vm.fundamentoLegal.idFundamento).then(function (resultado) {
                vm.fundamentoLegal = angular.copy(resultado.data.resposta[0]);
                vm.paginationFundamentosLegaisTipoAvaliacao = angular.copy(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList);
                vm.paginationFundamentosLegaisTipoAvaliacao = $filter('orderBy')(vm.paginationFundamentosLegaisTipoAvaliacao, 'ordem');
                integradorEscopoCompartilhadoService.limparEscopo();
                preencherConfiguracoesFundamentoLegal();
                if(vm.fundamentoLegal.utilizarFundamentoLegal){
                  vm.utilizarFundamento();
                }
                if(vm.isDetalhar){
                  $('[contenteditable]').attr('contenteditable', 'false');
                  $('[name=html]').attr('disabled', 'disabled');
                }
              });

            }
            if(!vm.fundamentoLegal.ativo && vm.fundamentoLegal.idFundamento){
              fundamentoLegalService.findDadosCancelamento(vm.fundamentoLegal.idFundamento).then(function (resultado) {
                vm.dadosCancelamento = angular.copy(resultado.data.resposta[0])
              })
            }
          }

          buscarInstrumentosFuncionalidade();
          buscarCriterios();
          buscarNaturezasPermitidas();
          buscarTodosFundamentos();
          buscarTiposIncorporacaoPermitidas();
          buscarTiposAquisicaoPermitidas();
          buscarEntidadesExtintas();
          buscarTiposAvaliacao();
        };

        var buscarInstrumentosFuncionalidade = function() {
          instrumentoFuncionalidadeService.buscarInstrumentosCadastradosAll().then(function(resp) {
            vm.listaInstrumentosFuncionalidade = resp.data.resposta;
          });
        };

        var buscarTiposAvaliacao = function() {
          fundamentoLegalService.buscarTiposAvaliacao().then(function(resp) {
            vm.listaTiposAvaliacao = resp.data.resposta;

            if(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList && vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList.length > 0){
              for(var i = 0; i < vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList.length; i++){
                for(var k = 0; k < vm.listaTiposAvaliacao.length; k++){
                  if(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList[i].tipoAvaliacao.id === vm.listaTiposAvaliacao[k].id){
                    vm.listaTiposAvaliacao.splice(k, 1);
                  }
                }
              }
            }
          });
        };

        var definirConfiguracaoFundamentoLegal = function (regiao, regiaoLabel, natureza, naturezaLabel, isDiferencaRegiao, isDiferencaNatureza) {
          var configuracao = angular.copy(configuracoesPadrao);
          configuracao.regiao = regiao;
          configuracao.regiaoLabel = regiaoLabel;
          configuracao.natureza = natureza;
          configuracao.naturezaLabel = naturezaLabel;
          configuracao.isDiferencaRegiao = isDiferencaRegiao;
          configuracao.isDiferencaNatureza = isDiferencaNatureza;
          return configuracao;
        };

        var buscarTodosFundamentos = function() {
          fundamentoLegalService.pesquisarTodosFundamentos().then(function(resp) {
            vm.listaFundamentosCadastrados = resp.data.resposta;
          });
        };

        var buscarCriterios = function() {
          fundamentoLegalService.buscarCriteriosUtilizado().then(function(resp) {
            vm.listaCriterios = resp.data.resposta;
          })
        };

        var buscarNaturezasPermitidas = function() {
          fundamentoLegalService.buscarNaturezasPermitidas().then(function(resp) {
            vm.listaNaturezasPermitidas = resp.data.resposta;
          });
        };

        var buscarTiposIncorporacaoPermitidas = function() {
          fundamentoLegalService.pesquisarTodosTiposIncorporacao().then(function(resp) {
            vm.listaIncorporacao = resp.data.resposta;
          });
        };

        var buscarTiposAquisicaoPermitidas = function() {
          fundamentoLegalService.pesquisarTodosTiposAquisicao().then(function(resp) {
            vm.listaAquisicao = resp.data.resposta;
          });
        };

        var buscarEntidadesExtintas = function() {
          fundamentoLegalService.pesquisarTodasEntidadesExtintas().then(function(resp) {
            vm.listaEntidadesExtintas = resp.data.resposta;
          });
        };

        $scope.$watch('fundamentoLegal.idInstrumentoFuncionalidade', function() {
          instrumentoFuncionalidadeService.buscarInstrumentosCadastradosAll().then(function(resp) {
            vm.listaInstrumentosFuncionalidade = resp.data.resposta;
            angular.forEach(vm.listaInstrumentosFuncionalidade, function(instrumentoFunc) {
              if (vm.fundamentoLegal.idInstrumentoFuncionalidade===instrumentoFunc.instrumentoFuncionalidade.id) {
                vm.moduloSelecionado = instrumentoFunc.instrumentoFuncionalidade.modulo.constante;
              }
            });
          });
        });

        vm.incrementarAreaValor = function(valor) {
          return valor + 0.01;
        };

        vm.fecharFundamentoLegal = function() {
          $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
        };

        var validarConfiguracoesFundamentoLegal = function() {
          if (vm.fundamentoLegal.isDiferencaRegiao==vm.configuracoesTodasTodas[0].isDiferencaRegiao
            && vm.fundamentoLegal.isDiferencaNatureza==vm.configuracoesTodasTodas[0].isDiferencaNatureza) {
            vm.fundamentoLegal.configuracoes = vm.configuracoesTodasTodas;
          } else if (vm.fundamentoLegal.isDiferencaRegiao==vm.configuracoesTodasNatureza[0].isDiferencaRegiao
            && vm.fundamentoLegal.isDiferencaNatureza==vm.configuracoesTodasNatureza[0].isDiferencaNatureza) {
            vm.fundamentoLegal.configuracoes = vm.configuracoesTodasNatureza;
          } else if (vm.fundamentoLegal.isDiferencaRegiao==vm.configuracoesRegiaoNatureza[0].isDiferencaRegiao
            && vm.fundamentoLegal.isDiferencaNatureza==vm.configuracoesRegiaoNatureza[0].isDiferencaNatureza) {
            vm.fundamentoLegal.configuracoes = vm.configuracoesRegiaoNatureza;
          } else if (vm.fundamentoLegal.isDiferencaRegiao==vm.configuracoesRegiaoTodas[0].isDiferencaRegiao
            && vm.fundamentoLegal.isDiferencaNatureza==vm.configuracoesRegiaoTodas[0].isDiferencaNatureza) {
            vm.fundamentoLegal.configuracoes = vm.configuracoesRegiaoTodas;
          }
        };

        vm.salvarFundamentoLegal = function(formularioDadosFundamentoLegal) {
          if(formularioDadosFundamentoLegal.$invalid){
            formularioDadosFundamentoLegal.$setSubmitted();
            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
            return;
          }

          validarConfiguracoesFundamentoLegal();

          fundamentoLegalService.salvar(vm.fundamentoLegal).then(function() {
            var mensagem;
            if(vm.isEditar){
              mensagem = 'Dados alterados com sucesso'
            }else{
              mensagem = 'Dados gravados com sucesso'
            }
            ComumFactory.mostrarMensagemSucesso(mensagem);
            $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
          });
        };

        vm.utilizarFundamento = function () {
          if(vm.fundamentoLegal.utilizarFundamentoLegal){
            fundamentoLegalService.findById(vm.fundamentoLegal.utilizarFundamentoLegal.id).then(function (resultado) {
              vm.fundamentoLegal2 = angular.copy(resultado.data.resposta[0]);
              for(var attr in vm.fundamentoLegal2){
                if(vm.fundamentoLegal2.hasOwnProperty(attr)) {
                  if(attr !== 'id' && attr !== 'template' && attr !== 'descricao' && attr !== 'idInstrumentoFuncionalidade' && attr !== 'utilizarFundamentoLegal'){
                    vm.fundamentoLegal[attr] = vm.fundamentoLegal2[attr];
                  }
                }
              }
              preencherConfiguracoesFundamentoLegal();
            });
            vm.bloquear = true;
          }else {
            for(var attr in vm.fundamentoLegal){
              if(vm.fundamentoLegal.hasOwnProperty(attr)) {
                if(attr !== 'id' && attr !== 'template' && attr !== 'descricao' && attr !== 'idInstrumentoFuncionalidade' && attr !== 'utilizarFundamentoLegal'){
                  if(attr !== 'utilizacoes'){
                    vm.fundamentoLegal[attr] = null;
                  }else{
                    vm.fundamentoLegal.utilizacoes = [];
                  }
                }
              }
            }
            vm.bloquear = false;
          }
        };

        vm.validarCriterioUtilizado = function () {

          return vm.fundamentoLegal.isExigirAtoAutorizativo && ((vm.fundamentoLegal.isSuperintendente && vm.fundamentoLegal.isSecretario) ||
                                                                (vm.fundamentoLegal.isMinistro && vm.fundamentoLegal.isSuperintendente) ||
                                                                (vm.fundamentoLegal.isMinistro && vm.fundamentoLegal.isSecretario));

        };

        var preencherConfiguracoesFundamentoLegal = function () {
          if (vm.fundamentoLegal.isDiferencaRegiao == false
            && vm.fundamentoLegal.isDiferencaNatureza == false) {
            for (var posicao in vm.fundamentoLegal.configuracoes){
              for(var attr in vm.fundamentoLegal.configuracoes[posicao]){
                if(vm.fundamentoLegal.configuracoes[posicao].hasOwnProperty(attr)) {
                  if((attr !== 'regiaoLabel' && attr !== 'naturezaLabel') || (attr === 'id' && vm.fundamentoLegal.id)){
                    vm.configuracoesTodasTodas[posicao][attr] = vm.fundamentoLegal.configuracoes[posicao][attr];
                  }
                }
              }
            }
          } else if (vm.fundamentoLegal.isDiferencaRegiao == false
            && vm.fundamentoLegal.isDiferencaNatureza==true) {
            for (var posicao in vm.fundamentoLegal.configuracoes){
              for(var attr in vm.fundamentoLegal.configuracoes[posicao]){
                if(vm.fundamentoLegal.configuracoes[posicao].hasOwnProperty(attr)) {
                  if((attr !== 'regiaoLabel' && attr !== 'naturezaLabel') || (attr === 'id' && vm.fundamentoLegal.id) ){
                    vm.configuracoesTodasNatureza[posicao][attr] = vm.fundamentoLegal.configuracoes[posicao][attr];
                  }
                }
              }
            }
          } else if (vm.fundamentoLegal.isDiferencaRegiao==true
            && vm.fundamentoLegal.isDiferencaNatureza==true) {
            for (var posicao in vm.fundamentoLegal.configuracoes){
              for(var attr in vm.fundamentoLegal.configuracoes[posicao]){
                if(vm.fundamentoLegal.configuracoes[posicao].hasOwnProperty(attr)) {
                  if((attr !== 'regiaoLabel' && attr !== 'naturezaLabel') || (attr === 'id' && vm.fundamentoLegal.id)){
                    vm.configuracoesRegiaoNatureza[posicao][attr] = vm.fundamentoLegal.configuracoes[posicao][attr];
                  }
                }
              }
            }
          } else if (vm.fundamentoLegal.isDiferencaRegiao==true
            && vm.fundamentoLegal.isDiferencaNatureza==false) {
            for (var posicao in vm.fundamentoLegal.configuracoes){
              for(var attr in vm.fundamentoLegal.configuracoes[posicao]){
                if(vm.fundamentoLegal.configuracoes[posicao].hasOwnProperty(attr)) {
                  if((attr !== 'regiaoLabel' && attr !== 'naturezaLabel') || (attr === 'id' && vm.fundamentoLegal.id)){
                    vm.configuracoesRegiaoTodas[posicao][attr] = vm.fundamentoLegal.configuracoes[posicao][attr];
                  }
                }
              }
            }
          }
        };

        vm.mostrarTipoAquisicao = function () {
          var b = false;
          for(var index in vm.fundamentoLegal.formaDeIncorporacaoPermitidas) {
            if(vm.fundamentoLegal.formaDeIncorporacaoPermitidas[index].descricao === 'Por Aquisição'){
              b = true;
            }
          }
          return b;
        };

        vm.mostrarEntidadeExtinta = function () {
          var b = false;
          for(var index in vm.fundamentoLegal.tipoAquisicaoPermitidas) {
            if(vm.fundamentoLegal.tipoAquisicaoPermitidas[index].descricao === 'Sucessão por Entidades da APF'){
              b = true;
            }
          }
          return b;
        };

        vm.selecionarTodosResponsavel = function (parametro) {
          if(parametro){
            vm.fundamentoLegal.naturezaJuridicaPermitidas = vm.listaNaturezasPermitidas;
          } else{
            vm.fundamentoLegal.naturezaJuridicaPermitidas = [];
          }
        };

        vm.selecionarTodasFormas = function (parametro) {
          if(parametro){
            vm.fundamentoLegal.formaDeIncorporacaoPermitidas = vm.listaIncorporacao;
          } else{
            vm.fundamentoLegal.formaDeIncorporacaoPermitidas = [];
          }
        };

        vm.selecionarTodasTiposAquisicao = function (parametro) {
          if(parametro){
            vm.fundamentoLegal.tipoAquisicaoPermitidas = vm.listaAquisicao;
          } else{
            vm.fundamentoLegal.tipoAquisicaoPermitidas = [];
          }
        };

        vm.selecionarTodasEntidadesExtintas = function (parametro) {
          if(parametro){
            vm.fundamentoLegal.entidadeExtintas = vm.listaEntidadesExtintas;
          } else{
            vm.fundamentoLegal.entidadeExtintas = [];
          }
        };

        vm.adicionarTipoAvaliacao = function(tipoAvaliacao){
          if(!tipoAvaliacao){
            return;
          }
          !vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList ? vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList = [] : "";

          vm.fundamentoLegalTipoAvaliacao.tipoAvaliacao = {};
          vm.fundamentoLegalTipoAvaliacao.ordem = null;
          vm.fundamentoLegalTipoAvaliacao.tipoAvaliacao = angular.copy(tipoAvaliacao);

          vm.paginationFundamentosLegaisTipoAvaliacao.length > 0 ? vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList = angular.copy(vm.paginationFundamentosLegaisTipoAvaliacao) : "";

          vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList.push(angular.copy(vm.fundamentoLegalTipoAvaliacao));

          if(vm.fundamentoLegal.metodologiaAvaliacao === true){
            for(var i = 0; i < vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList.length; i++){
              if(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList[i].ordem == null){
                vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList[i].ordem = i + 1;
              }
            }

            vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList = $filter('orderBy')(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList, 'ordem');
          }

          for(var k = 0; k < vm.listaTiposAvaliacao.length; k++){
            if(vm.listaTiposAvaliacao[k].id === tipoAvaliacao.id){
              vm.listaTiposAvaliacao.splice(k, 1);
            }
          }

          vm.paginacaoTipoAvaliacao();
          vm.fundamentoLegal.tipoAvaliacao = null;
        };

         vm.removerTipoAvaliacao = function(index){
           for(var i = 0; i < vm.paginationFundamentosLegaisTipoAvaliacao.length; i++){
             if(i === index){
               vm.listaTiposAvaliacao.push(vm.paginationFundamentosLegaisTipoAvaliacao[i].tipoAvaliacao);
               vm.paginationFundamentosLegaisTipoAvaliacao.splice(i, 1);
             }
           }

           vm.ordenarArray();
         };

         vm.ordenarArray = function(){
           for(var i = 0; i < vm.paginationFundamentosLegaisTipoAvaliacao.length; i++){
             vm.paginationFundamentosLegaisTipoAvaliacao[i].ordem = i + 1;
           }

           vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList = angular.copy(vm.paginationFundamentosLegaisTipoAvaliacao);
         };

        vm.incrementarOrdem = function (arrayFundamentoLegalTipoAvaliacao, ordem) {
          for (var i = 0; i < arrayFundamentoLegalTipoAvaliacao.length; i++) {
            if (arrayFundamentoLegalTipoAvaliacao[i].ordem === ordem) {
              arrayFundamentoLegalTipoAvaliacao[i].ordem = arrayFundamentoLegalTipoAvaliacao[i].ordem + 1;
            } else if (arrayFundamentoLegalTipoAvaliacao[i].ordem === (ordem + 1)) {
              arrayFundamentoLegalTipoAvaliacao[i].ordem = arrayFundamentoLegalTipoAvaliacao[i].ordem - 1;
            }
          }

          vm.paginationFundamentosLegaisTipoAvaliacao = $filter('orderBy')(vm.paginationFundamentosLegaisTipoAvaliacao, 'ordem');
        };

        vm.decrementarOrdem = function (arrayFundamentoLegalTipoAvaliacao, ordem) {
          for (var i = 0; i < arrayFundamentoLegalTipoAvaliacao.length; i++) {
            if (arrayFundamentoLegalTipoAvaliacao[i].ordem === ordem) {
              arrayFundamentoLegalTipoAvaliacao[i].ordem = arrayFundamentoLegalTipoAvaliacao[i].ordem - 1;

            } else if (arrayFundamentoLegalTipoAvaliacao[i].ordem === (ordem - 1)) {
              arrayFundamentoLegalTipoAvaliacao[i].ordem = arrayFundamentoLegalTipoAvaliacao[i].ordem + 1;
            }
          }

          vm.paginationFundamentosLegaisTipoAvaliacao = $filter('orderBy')(vm.paginationFundamentosLegaisTipoAvaliacao, 'ordem');
        };

        vm.paginacaoTipoAvaliacao = function (nextPage, novoTamanhoLimite) {
          vm.paginationFundamentosLegaisTipoAvaliacao = [];
          vm.totalItems = angular.copy(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList.length);
          novoTamanhoLimite ? vm.tamanhoLimite = novoTamanhoLimite :"";

          if (vm.totalItems < vm.tamanhoLimite) {
            vm.paginationFundamentosLegaisTipoAvaliacao = angular.copy(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList);
          } else {
            for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
              vm.paginationFundamentosLegaisTipoAvaliacao.push(angular.copy(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList[i]));
            }
          }
        };

        vm.validaPercentual = function(percentual){
          if(percentual == 0 || percentual > 100){
            ComumFactory.mostrarMensagemError($filter('translate')('erro-percentual-invalido'));
            vm.fundamentoLegal.percentual = null;
          }
        };

        init();
      }]);
})();

(function () {
  'use strict';

  angular
    .module('su-integrador')
    .factory('documentosFactory',
      ["comumIntegradorService", "$http", "ComumFactory", function (comumIntegradorService, $http, ComumFactory) {

        var URL_DOCUMENTO_PESSOA = "/integrador/api/documento";

        var URL_INTEGRADOR_COMUM = "/integrador/api/comum";

        var getFormatoArquivoSelecionado = function (novoDocumento, listaFormatosArquivos) {
          novoDocumento.formatoArquivos = [];

          angular.forEach(listaFormatosArquivos, function (formatoDoc) {
            if(formatoDoc.selecionado){
              novoDocumento.formatoArquivos.push(formatoDoc);

            }
          })
        };

        var limparFormatosSelecionados = function (listaFormatosArquivos, callback) {
          angular.forEach(listaFormatosArquivos, function (doc) {
            doc.selecionado = false;

          });

          callback;

        };

        var findExtensao = function (arrayFormatos, extensao) {
          for (var i = 0; i < arrayFormatos.length; i++) {
            if (extensao == arrayFormatos[i].formato)
              return true;
          }
          return false;
        };

        var selecionarCheckboxsFormatosArq = function (novoDocumento,listaFormatosArquivos ) {
          angular.forEach(novoDocumento.formatoArquivos, function (formatoSelecionado) {
            angular.forEach(listaFormatosArquivos, function (formatoArray) {
              if (formatoSelecionado.id == formatoArray.id) {
                formatoArray.selecionado = true;
              }
            });
          });
        };

        var addDocumento = function ($event, form, documentos, novoDocumento,
                                     statusExtensaoDocumento, outraExtensaoDocumento,
                                     listaFormatosArquivos, itemOld, isEdicao, callback) {

          form.$submitted = true;
          var extensao = '';

          getFormatoArquivoSelecionado(novoDocumento, listaFormatosArquivos);

          if (statusExtensaoDocumento && outraExtensaoDocumento != "") {
            extensao = outraExtensaoDocumento.replace(/[\W]+/g, "").toUpperCase();

            var resp = findExtensao(listaFormatosArquivos, extensao);

            if (resp) {
              ComumFactory.mostrarMensagemError('O formato de extensão informado já existe.');
              return;
            } else {
              novoDocumento.formatoArquivos.push({
                'formato': extensao
              });
            }
          }

          $event.preventDefault();


          if (isEdicao) {

            for(var i=0; i<documentos.length;i++){

              if (documentos[i].nome == itemOld.nome) {

                documentos[i] = angular.copy(novoDocumento);
              }
            }

          } else {
            novoDocumento.status = true;
            documentos.push(novoDocumento);
          }

          callback();

        };

        var buscarDocumentosPessoa = function(filtroConsulta, offset, limit) {
          return $http.get(URL_DOCUMENTO_PESSOA, {
            params: {
              nome: filtroConsulta.nome,
              pessoaFisica: filtroConsulta.pessoaFisica,
              pessoaJuridica: filtroConsulta.pessoaJuridica,
              imovel: filtroConsulta.imovel,
              situacao: filtroConsulta.situacao,
              offset: offset,
              limit: limit
            }
          });
        };

        var consultarDocumentoPessoa = function(idDocumento) {
          return $http.get(URL_DOCUMENTO_PESSOA + '/consultar/' + idDocumento);
        };

        var listarFormatosArquivo = function () {
          return $http.get(URL_INTEGRADOR_COMUM + '/formato-arquivo/');
        };

        var salvar =function (data) {
          return $http.post(URL_DOCUMENTO_PESSOA + '/salvar/', data);
        };

        return {
          getFormatoArquivoSelecionado: getFormatoArquivoSelecionado,
          limparFormatosSelecionados: limparFormatosSelecionados,
          addDocumento: addDocumento,
          selecionarCheckboxsFormatosArq: selecionarCheckboxsFormatosArq,
          buscarDocumentosPessoa: buscarDocumentosPessoa,
          consultarDocumentoPessoa: consultarDocumentoPessoa,
          listarFormatosArquivo: listarFormatosArquivo,
          salvar: salvar
        };

      }])

}());

(function () {

  'use strict';
  angular
    .module('su-integrador')
    .controller('consultarDocumentoPessoaController',
      ["$scope", "$state", "documentosFactory", "integradorEscopoCompartilhadoService", function ($scope, $state, documentosFactory, integradorEscopoCompartilhadoService) {

        var vm = $scope;

        vm.filtroConsulta = {};

        var init = function () {
          vm.registroInicial = 1;
          vm.tamanhoLimite = 5;
          vm.totalItems = 0;

          vm.listagemDocumentosPessoa = [];
          vm.paginadosDocumentosPessoa = [];
          vm.consultarDocumentosPessoa();
        };

        vm.consultarDocumentosPessoa = function() {
          documentosFactory.buscarDocumentosPessoa(vm.filtroConsulta)
            .then(function(resp) {
              vm.listagemDocumentosPessoa = resp.data.resposta;
              vm.totalItems = resp.data.resposta.length;
              vm.paginarDocumentosPessoa();
            });
        };

        vm.redirecionarCadastrarDocumentosPessoa = function() {
          $state.go('INTEGRADOR.CADASTRO_DOCUMENTO');
        };

        vm.limparConsulta = function() {
          vm.filtroConsulta = {};
        };

        vm.exibirFormatosArquivoDocumento = function(documento) {
          if (documento.formatoArquivos) {
            var cont = 0;
            var labelFormatos = '';
            angular.forEach(documento.formatoArquivos,  function(formatoArquivo){
              if (cont > 0) {
                labelFormatos += ', ';
              }
              labelFormatos += '.' + formatoArquivo.formato.toLowerCase();
              cont++;
            });
            return labelFormatos;
          }
        };

        vm.exibirOutrosFormatos = function (documento) {
          var labelOutrosDocumentos ='';
          if(documento.outro !== null){
          labelOutrosDocumentos += '.'+ documento.outro;
          return labelOutrosDocumentos;
          }
        };



        vm.validarSituacaoDocumento = function(situacao) {
          if (situacao) {
            return 'ativo';
          } else {
            return 'inativo';
          }
        };

        vm.paginarDocumentosPessoa = function() {
          vm.paginadosDocumentosPessoa = [];
          vm.totalItems = vm.listagemDocumentosPessoa.length;
          if (vm.totalItems < vm.tamanhoLimite) {
            vm.paginadosDocumentosPessoa = vm.listagemDocumentosPessoa;
          } else {
            for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
              vm.paginadosDocumentosPessoa.push(vm.listagemDocumentosPessoa[i]);
              if (vm.paginadosDocumentosPessoa.length == vm.tamanhoLimite) {
                break;
              }
            }
          }
        };

        vm.redirecionarEditarDocumentoPessoa = function(idDocumento) {
          integradorEscopoCompartilhadoService.setObjetos('idDocumento', idDocumento);
          $state.go('INTEGRADOR.EDITAR_DOCUMENTO');
        };

        init();
      }])
})();



(function () {

  'use strict';
  angular
    .module('su-integrador')
    .controller('cadastroDocumentoController',
      ["$scope", "$stateParams", "documentosFactory", "$state", "$rootScope", "$timeout", "$q", "$filter", "ComumFactory", "formularioIntegradorService", "integradorEscopoCompartilhadoService", function ($scope, $stateParams, documentosFactory, $state, $rootScope,
                $timeout, $q, $filter, ComumFactory, formularioIntegradorService, integradorEscopoCompartilhadoService) {

        var vm = $scope;
        var validaFormulario = formularioIntegradorService.getInstance();
        vm.validarFormulario = validaFormulario.validarFormulario;
        vm.isEditar = false;

        var init = function () {
          vm.listaFormatosArquivos = [];
          carregarFormatosArquivo();

          if (integradorEscopoCompartilhadoService.getObjeto('idDocumento')) {
            var idDocumento = integradorEscopoCompartilhadoService.getObjeto('idDocumento');
            documentosFactory.consultarDocumentoPessoa(idDocumento).then(function (resp) {
              vm.documento = resp.data.resultado;
              vm.statusExtensaoDocumento = vm.documento.outro ? true : false;
              angular.forEach(vm.listaFormatosArquivos, function (formatoArquivo) {
                angular.forEach(vm.documento.formatoArquivos, function (formatoDocumento) {
                  if (formatoArquivo.id == formatoDocumento.id) {
                    formatoArquivo.selecionado = true;
                  }
                })
              });
            });
            vm.isEditar = true;
            integradorEscopoCompartilhadoService.limparEscopo();
          } else {
            vm.documento = {
              documentos: [],
              formatoArquivos: [],
              pessoaFisica: false,
              pessoaJuridica: false,
              imovel: false
            };
          }

          vm.listas = {
            documento: []

          };

          vm.novoDocumento = {};
          vm.formatoArquivos = [];
          montarListaDocumento();
        };
        init();

        function carregarFormatosArquivo() {
          documentosFactory.listarFormatosArquivo().then(function (resposta) {
            vm.listaFormatosArquivos = resposta.data.resposta;
          });
        }

        var limparFormatos = function () {
          documentosFactory.limparFormatosSelecionados(vm.listaFormatosArquivos,
            function () {
              vm.statusExtensaoDocumento = false;
              vm.outraExtensaoDocumento = "";
            });
        };

        vm.addDocumento = function ($event) {

          documentosFactory.addDocumento(
            $event, vm.adicionarDocumentos, vm.documentos,
            vm.novoDocumento, vm.statusExtensaoDocumento,
            vm.outraExtensaoDocumento, vm.listaFormatosArquivos,
            function () {

              vm.novoDocumento = {
                nome: undefined
              };

              limparFormatos();
              vm.adicionarDocumentos.$submitted = false;
            });
        };
        vm.limparCampos = function () {
          vm.id = vm.documento.id;
          vm.pessoaFisica = vm.documento.pessoaFisica;
          vm.pessoaJuridica = vm.documento.pessoaJuridica;
          vm.imovel = vm.documento.imovel;
          vm.documento = {};
          vm.statusExtensaoDocumento = false;
          vm.listas = {};
          init();
          vm.documento.id = vm.id;
          vm.documento.pessoaFisica = vm.pessoaFisica;
          vm.documento.pessoaJuridica = vm.pessoaJuridica;
          vm.documento.imovel = vm.imovel;
        };

        vm.fechar = function () {
          $state.go('INTEGRADOR.CONSULTAR_DOCUMENTO_PESSOA');
        };

        function salvarDocumento() {
          vm.documento.formatoArquivos = getExtensoesSelecionadas();
          documentosFactory.salvar(vm.documento).then(function (resposta) {
            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-documentos'))
            $state.go('INTEGRADOR.CONSULTAR_DOCUMENTO_PESSOA');
          });

        }

        vm.validarFormulario = function () {
          if (vm.formCadastroDocumentos.$valid && validarVinculo() && validarExtensoes()) {
            salvarDocumento();
          } else {
            vm.formCadastroDocumentos.$setSubmitted();
            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
          }
        };

        function getExtensoesSelecionadas() {
          var formatosSelecionado = [];
          angular.forEach(vm.listaFormatosArquivos, function (formato) {
            if (formato.selecionado) {
              formatosSelecionado.push(formato);
            }
          });
          return formatosSelecionado;
        }

        function validarVinculo() {
          return vm.documento.pessoaFisica || vm.documento.pessoaJuridica || vm.documento.imovel;
        }

        function validarExtensoes() {
          return getExtensoesSelecionadas().length > 0 || vm.statusExtensaoDocumento;
        }

        function montarListaDocumento () {
        angular.forEach(vm.listas.documento, function (item) {
          if (item.id == undefined || item.id == 0) {
            if (!vm.documento.documentos) {
              vm.documento.documentos = [];
            }
            vm.documento.documentos.push(item);
          } else {

            for (var i = 0; i < vm.documento.documentos.length; i++) {
              if (item.id == vm.documento.documentos[i].id) {
                vm.documento.documentos[i] = angular.copy(item);
                return;
              }
            }
          }
        });
      }
      }])
})();



(function () {

  'use strict';

  service.$inject = ["$http"];
  angular
    .module('su-integrador')
    .factory('consultaOrientacaoTecnicaService', service);

  function service ($http) {

    var url = "integrador/api/orientacaoTecnica";

    var pesquisar = function(filtro){
      return $http.get(url + "/pesquisaOrientacaoTecnica/?"+generateFilter(filtro)+"areas=piru,rola,pinto,piru");
    };

    var generateFilter = function(filtro){
      var string = "";
      for(var key in filtro){
        if(filtro.hasOwnProperty(key)){
          string += key+'='+filtro[key]+'&';
        }
      }
      return string;
    };

    return {
      pesquisar: pesquisar
    }

  }

})();

(function () {
  'use strict';
  angular
    .module('su-integrador')
    .controller('consultaOrientacaoTecnicaController',
      ['$scope', '$element', 'ComumFactory', '$mdDialog', 'consultaOrientacaoTecnicaService', '$state', 'integradorEscopoCompartilhadoService', 'orientacaoTecnicaService',
        function ($scope, $element, ComumFactory, $mdDialog, consultaOrientacaoTecnicaService, $state, integradorEscopoCompartilhadoService, orientacaoTecnicaService) {

          var vm = $scope;

          vm.listaOrientacaoTecnica = [];

          vm.filtro = {
            offset: 0,
            limit: 5
          };

          $element.find('input').on('keydown', function(ev){
            ev.stopPropagation();
          });

          var init = function(){
            vm.pesquisar();

            orientacaoTecnicaService.getAreasOrientacaoTecnica().then(function(resp){
              vm.areasOrientacaoTecnica = resp.data.resposta;
            });

            orientacaoTecnicaService.getAssuntosOrientacaoTecnica().then(function(resp){
              vm.assuntosOrientacaoTecnica = resp.data.resposta;
            });
          };

          vm.clearFilter = function(){
            vm.filtro = {
              offset: 0,
              limit: 5
            };
          };

          vm.novoCadastro = function(){
            $state.go('INTEGRADOR.CADASTRO_ORIENTACAO_TECNICA');
          };

          vm.clearSearchAssunto = function(){
            vm.searchAssunto = "";
          };

          vm.clearSearchArea = function(){
            vm.searchArea = "";
          };

          vm.pesquisar = function(){
            consultaOrientacaoTecnicaService.pesquisar(vm.filtro).then(function(resp){
              vm.listaOrientacaoTecnica = resp.data.resposta[0].content;
            });
          };

          init();

        }]);
})();

(function(){
    'use strict';

        angular
        .module('su-integrador')
        .service('atosContratosService', ['$http', '$filter', 'comumIntegradorService', service]);

        function service($http, $filter, comumIntegradorService){

            var urlComum = 'integrador/api/comum';
            var URL_ATOS_CONTRATOS = 'integrador/api/atos-contratos';

            var GESTAO_PRAIA = 2;

            var buscarTipoModeloContrato = function(){
                return $http.get(urlComum + '/pesquisar-tipo-modelo-contratos');
            };

            var buscarTipoCompetenciaAssinatura = function(){
                return $http.get(urlComum + '/pesquisar-tipo-competencia-assinatura');
            };

            var buscarModulos = function(){
                return $http.get('integrador/api/modulos');
            };

            var buscarFuncionalidadePorModulo = function(id){
                return $http.get('integrador/api/instrumento-funcionalidade/pesquisar-por-modulo/' + id);
            };

            var buscarAlterarAtoContrato = function(idContrato) {
              return $http.get(URL_ATOS_CONTRATOS + '/buscar-entidades/' + idContrato)
            }

            var buscarAtoContrato = function(idContrato) {
                return $http.get(URL_ATOS_CONTRATOS + '/buscar/' + idContrato)
            };

            var buscarDespachoPorFuncionalidade = function (id) {
                return $http.get('integrador/api/instrumento-funcionalidade/pesquisa-despacho-por-funcionalidade/' + id);
            };

            var excluirModeloAtosContratos = function(atosContratos) {
                return $http.post(URL_ATOS_CONTRATOS + '/deletar', atosContratos);
            };

            var salvar = function(data){
                return $http.post(URL_ATOS_CONTRATOS + '/salvar', data);
            };

            var buscarCodPrefixo = function (prefixo) {
                return $http.get(URL_ATOS_CONTRATOS + '/gerar-prefixo/'+ prefixo);
            };

            var buscarFundamentoLegalFuncionalidade = function (id) {
                return $http.get('integrador/api/fundamento-legal/pesquisar-fundamento-legal/' + id);
            }

            // var buscarFundamentoLegalPorTipo = function (tipo) {
            //     return $http.get('integrador/api/fundamento-legal/pesquisar-por-tipo/' + tipo);
            // };

            var selecionarTodos = function (principal) {
              principal.status = !principal.status;

                angular.forEach(principal.item, function (item) {
                  item.status = principal.status;
                });
            };

            var buscarTodosAtosContratos = function(offset, limit) {
                return $http.get(URL_ATOS_CONTRATOS, {
                  params: {
                    offset: offset,
                    limit: limit
                  }
                });
            };

            var filterBuscarTodosAtosContratos = function(filtro, offset, limit) {
              return $http.get(URL_ATOS_CONTRATOS, {
                params: {
                  descricao: filtro.descricao,
                  modulo: filtro.modulo,
                  tipoModeloContrato: filtro.tipoModeloContrato,
                  instrumentoFuncionalidade: filtro.instrumentoFuncionalidade,
                  offset: offset,
                  limit: limit
                }
              });
            };

            var getConfiguracaoInicialContratoTermo = function (tipo) {
              return [{
                        status: true,
                        ordem: 0,
                        item: [{descricao: 'QUALIFICAÇÃO DAS PARTES', status: true, hierarquia: 'PRIMARIO', identificador: 'QUALIFICACAO_DAS_PARTES'},
                               {
                                 descricao: undefined,
                                 status: false,
                                 hierarquia: 'SECUNDARIO',
                                 identificador: 'DENOMINACAO_UNIAO',
                                 label: undefined,
                                 item: [{descricao: '[nome de quem assina], [cargo], CPF', status: true, hierarquia: 'TERCIARIO', identificador: 'DADOS_DA_PESSOA'}]
                               },
                               {
                                 descricao: undefined,
                                 status: false,
                                 hierarquia: 'SECUNDARIO',
                                 identificador: 'DENOMINACAO_TERCEIRO',
                                 item: [{descricao: '[Nome da pessoa]', status: true, hierarquia: 'TERCIARIO', identificador: 'NOME_PESSOA'},
                                        {descricao: 'Endereço', status: true, hierarquia: 'TERCIARIO', identificador: 'ENDERECO_MUNICIPIO'},
                                        {descricao: 'Endereço', status: true, hierarquia: 'TERCIARIO', identificador: 'ENDERECO_PREFEITO'}]
                               }]
                      },{
                        status: true,
                        ordem: 1,
                        item: [{descricao: 'IMÓVEL', status: true, hierarquia: 'PRIMARIO', label: undefined, identificador: 'IMOVEL'},
                                {descricao:'RIP', status: true, hierarquia: 'SECUNDARIO', identificador: 'RIP'},
                                {descricao:'ENDEREÇO', status: true, hierarquia: 'SECUNDARIO', identificador: 'ENDERECO'},
                                {descricao:'ÁREA DO TERRENO', status: true, hierarquia: 'SECUNDARIO', identificador: 'AREA_TERRENO_CONTRATO'},
                                {descricao:'NATUREZA', status: true, hierarquia: 'SECUNDARIO', identificador: 'NATUREZA_CONTRATO'},
                                {descricao:'MATRÍCULA CARTORIAL', status: true, hierarquia: 'SECUNDARIO', identificador: 'MATRICULA_CARTORIAL'},
                                {descricao:'MEMORIAL DESCRITIVO', status: true, hierarquia: 'SECUNDARIO', identificador: 'MEMORIAL_DESCRITIVO'}]
                    },{
                        status: true,
                        ordem: 2,
                        item: [{descricao: 'PRAZO', status: true, hierarquia: 'PRIMARIO', label: '[traz o prazo definido no instrumento/funcionalidade]', identificador: 'PRAZO_CONTRATO'}]
                    },{
                        status: true,
                        ordem: 3,
                        item: [{descricao: 'FINALIDADE', status: true, hierarquia: 'PRIMARIO', identificador: 'FINALIDADE_CONTRATO'},
                               {descricao:'TIPO', status: true, hierarquia: 'SECUNDARIO', identificador: 'TIPO_CONTRATO'},
                               {descricao:'ESPECIFICAÇÃO', status: true, hierarquia: 'SECUNDARIO', identificador: 'ESPECIFICACAO_CONTRATO'},
                               {descricao:'DESCRIÇÃO', status: true, hierarquia: 'SECUNDARIO', identificador: 'DESCRICAO_CONTRATO'}]
                    },
                    {
                        status: true,
                        ordem: 4,
                        item: [{descricao: 'ENCARGO(S)', status: true, hierarquia: 'PRIMARIO', label: '[traz os encargos definid]', identificador: 'ENCARGOS_CONTRATO'}]
                    },{
                        status: true,
                        ordem: 5,
                        item: [{descricao: 'PREÇOS E CONDIÇÕES DE PAGAMENTO', status: true, hierarquia: 'PRIMARIO', identificador: 'PRECOS_CONDICOES_PAGAMENTO_CONTRATO'}]
                    },{
                        status: true,
                        ordem: 6,
                        item: [{descricao: 'CONDIÇÕES', status: true, hierarquia: 'PRIMARIO', identificador: 'CONDICOES_CONTRATO'}]
                    }
                  ];
            };


            var getConfiguracaoDeclaracaoCertidao = function (tipo) {
              return [{
                        status: true,
                        ordem: 0,
                        item: [{descricao: 'NOME/RAZÃO SOCIAL', status: true, hierarquia: 'PRIMARIO', identificador: 'NOME_RAZAO_SOCIAL'},
                               {descricao: 'CNPJ/CPF', status: true, hierarquia: 'PRIMARIO', identificador: 'CNPJ_CPF'},
                               {descricao: 'Nº DO ATENDIMENTO', status: true, hierarquia: 'PRIMARIO', identificador: 'NUMERO_ATENDIMENTO'}]
                      },{
                        status: true,
                        ordem: 1,
                        item: [{descricao: 'IMÓVEL', status: true, hierarquia: 'PRIMARIO', identificador: 'IMOVEL'},
                               {descricao:'RIP', status: true, hierarquia: 'SECUNDARIO', identificador: 'RIP'},
                               {descricao:'ENDEREÇO', status: true, hierarquia: 'SECUNDARIO', identificador: 'ENDERECO'},
                               {descricao:'ÁREA TOTAL DO TERRENO', status: true, hierarquia: 'SECUNDARIO', identificador: 'AREA_TOTAL_TERRENO'},
                               {descricao:'ÁREA TOTAL DA UNIÃO', status: true, hierarquia: 'SECUNDARIO', identificador: 'AREA_TOTAL_UNIAO'},
                               {descricao:'FRAÇÃO IDEAL', status: true, hierarquia: 'SECUNDARIO', identificador: 'FRACAO_IDEAL'},
                               {descricao:'REGIME', status: true, hierarquia: 'SECUNDARIO', identificador: 'REGIME'},
                               {descricao:'NATUREZA DA TRANSIÇÃO', status: true, hierarquia: 'SECUNDARIO', identificador: 'NATUREZA_TRANSICAO'},
                               {descricao:'RESPONSÁVEL ATUAL', status: true, hierarquia: 'SECUNDARIO', identificador: 'RESPONSAVEL_ATUAL'}]
                    },{
                        status: true,
                        ordem: 2,
                        item: [{descricao: 'CONTEÚDO', status: false, hierarquia: 'PRIMARIO', identificador: 'TITULO_CONTEUDO'},
                               {descricao: undefined, status: false, hierarquia: 'SECUNDARIO', identificador: 'VALOR_CONTEUDO'}]
                    },{
                        status: true,
                        ordem: 3,
                        item: [{descricao: undefined, status: true, hierarquia: 'PRIMARIO', identificador: 'COMPETENCIA_ASSINATURA'}]
                    },{
                        status: true,
                        ordem: 4,
                        item: [
                              {descricao: 'Data de Emissão: ', status: true, hierarquia: 'SECUNDARIO', identificador: 'DATA_EMISSAO'},
                              {descricao: 'Hora da Emissão: ', status: true, hierarquia: 'SECUNDARIO', identificador: 'HORA_EMISSAO'},
                              {descricao: 'Validade: ', status: true, hierarquia: 'SECUNDARIO', label: undefined, identificador: 'VALIDADE'},
                              {descricao: 'Código de Controle da Certidão/Declaração: ', status: true, hierarquia: 'SECUNDARIO', identificador: 'CODIGO_CONTROLE'},
                              {descricao: 'Esta certidão/declaração é fornecida gratuitamente, e sua autenticidade poderá ser confirmada no endereço: ', status: true, hierarquia: 'SECUNDARIO', identificador: 'AUTENTICIDADE_CONFIRMACAO_ENDEDERECO'}]
                    }
                ];
            };

            var getConfiguracaoPreview = function () {
              return [{label: 'titulo', valorLabel: 'MINISTÉRIO DO PLANEJAMENTO, DESENVOLVIMENTO E GESTÃO SECRETARIA DO PATRIMÔNIO DA UNIÃO'},
                      {label: 'textoUniao', valorLabel: 'União, por intermédio da Secretaria do Patrimônio da União, representado neste ato por:'},
                      {label: 'dataGerada', valorLabel: comumIntegradorService.diaExtenso()}];
            }

            var atualizarDicionario = function(item, descricao, label){
                angular.extend(item,{
                  descricao: angular.copy(descricao),
                  status: true,
                  label: label
                });

                console.log(label)
            };

            var atualizarClausulas = function(clausulas, config){
                angular.forEach(clausulas, function (clausulas, $index) {
                    config.item.push({
                    descricao: 'CLAUSULAS',
                    label: angular.copy(clausulas.descricao),
                    salvar: false,
                    hierarquia: 'SECUNDARIO',
                    identificador: getNumeroPorExtenso($index)
                  });
                });
            };

            var atualizarDicionarioDeclaracao = function (atosContratos, configuracaoComponentes) {
                angular.forEach(configuracaoComponentes, function (config) {
                  angular.forEach(config.item, function (item) {

                    if(item.identificador == 'VALOR_CONTEUDO'){
                      atualizarDicionario(item, atosContratos.conteudos.descricao);
                    }else if(item.identificador == 'COMPETENCIA_ASSINATURA'){
                      atualizarDicionario(item, atosContratos.tipocompetenciaAssinatura.descricao);
                    }else if(item.identificador == 'VALIDADE'){
                      atualizarDicionario(item, atosContratos.prazo);
                    }

                  });
                });
            };


            var atualizarPraias = function(configuracaoComponentes){
              var DADOS_IMOVEL = ['RIP','ENDERECO', 'AREA_TERRENO_CONTRATO',
                                      'NATUREZA_CONTRATO', 'MATRICULA_CARTORIAL', 'MEMORIAL_DESCRITIVO'];

                  angular.forEach(configuracaoComponentes, function(config){
                      var length = config.item.length;
                      var entrou = false;

                      for(var i=0; i<length-1; i++){
                        if(config.item[i] &&
                              DADOS_IMOVEL.indexOf(config.item[i].identificador) != -1){
                          config.item.splice(i,1);
                          entrou = true;
                          i--;
                        }
                      }

                      if(entrou){
                        entrou = false;
                        config.item.push({
                          descricao: 'Praias urbanas do Município',
                          status: true,
                          hierarquia: 'SECUNDARIO',
                          identificador: 'PRAIAS_URBANAS'
                        });
                      }
                  });
            };

            var atualizarDicionarioContrato = function (atosContratos, configuracaoComponentes) {

                angular.forEach(configuracaoComponentes, function(config){
                  var length = config.item.length;
                  for(var i=0; i<length; i++){
                    if(config.item[i].identificador == 'CONDICOES_CONTRATO'){
                        atualizarClausulas(atosContratos.clausulas, config);
                    }else if(config.item[i].identificador == 'DENOMINACAO_UNIAO'){
                      atualizarDicionario(config.item[i], atosContratos.denominacaoPartes.uniao,
                          getLabel('textoUniao', atosContratos.configuracaoAtosContratos));
                    }else if(config.item[i].identificador == 'DENOMINACAO_TERCEIRO'){
                      atualizarDicionario(config.item[i], atosContratos.denominacaoPartes.terceiro);
                    }
                  }
                });

                if(atosContratos.instrumentoFuncionalidade.id === GESTAO_PRAIA){
                      atualizarPraias(configuracaoComponentes);
                }
            };


            var atualizarModelo = function(atosContratos, configuracaoComponentes){

              angular.forEach(configuracaoComponentes, function(config){
                  angular.forEach(config.item, function(item){
                    if(atosContratos.denominacaoPartes){

                      if(atosContratos.denominacaoPartes.uniao && item.identificador == 'DENOMINACAO_UNIAO'){
                        atosContratos.denominacaoPartes.uniao = item.descricao;
                      }else if(atosContratos.denominacaoPartes && item.identificador == 'DENOMINACAO_TERCEIRO'){
                        atosContratos.denominacaoPartes.terceiro = item.descricao;
                      }

                    }else if(atosContratos.prazo && item.identificador == 'VALIDADE'){
                      atosContratos.prazo = parseInt(item.label);
                    }
                  });
              });
            };


            var getLabel = function (label, config) {
              var retorno = undefined;
              angular.forEach(config, function (item) {
                if(item.label == label){
                  retorno = item.valorLabel;
                }
              });
              return retorno;
            };

            var getDicionarioFinal = function (dictConfigurarComponente) {
              angular.forEach(dictConfigurarComponente, function(config){
                for(var i=0; i< config.item.length; i++){
                  if(config.item[i].salvar == false){
                     config.item.splice(i,1);
                     i--;
                  }
                }
              });

              return angular.copy(dictConfigurarComponente);
            };

            var getValorPorLabel = function (dictConfigurarComponente, indetificador) {
              var item = undefined;
              angular.forEach(dictConfigurarComponente, function(config){
                for(var i=0; i< config.item.length; i++){
                  if(config.item[i].identificador == indetificador){
                     item = config.item[i];
                  }
                }
              });
              return item.label;
            };

            var ordenaLista = function (atual, destino, lista) {
              var item = lista[atual];
              lista[atual] = lista[destino];
              lista[destino] = item;
            };

            var setarOrdem = function (configuracaoComponentes) {
              angular.forEach(configuracaoComponentes, function(config, $index){
                config.ordem = $index;
              });
            };

            var getNumeroPorExtenso = function(numero){
               var primeiraCasaDecimal = ['Primeira', 'Segunda', 'Terceira', 'Quarta', 'Quinta', 'Sexta', 'Sétima', 'Oitava', 'Nona', 'Décima'];
               var segundaCasaDecimal = ['Décima', 'Vigésima', 'Trigésima', 'Quadragésima', 'Quinquagésimo', 'Sexagésimo', 'Septuagésimo', 'Octogésimo', 'Nonagésimo'];

               var ordinal = '';

               if(numero < 10){
                  ordinal = primeiraCasaDecimal[numero];
               }else{
                  var strs =  numero.toString().split('');
                  ordinal = segundaCasaDecimal[parseInt(strs[0])-1];
                  ordinal += ' ' + primeiraCasaDecimal[strs[1]];
               }

               return $filter('translate')('clausula') + ' ' + ordinal;
             };



            return {
                buscarTipoModeloContrato: buscarTipoModeloContrato,
                buscarDespachoPorFuncionalidade: buscarDespachoPorFuncionalidade,
                buscarTipoCompetenciaAssinatura: buscarTipoCompetenciaAssinatura,
                getConfiguracaoInicialContratoTermo: getConfiguracaoInicialContratoTermo,
                getConfiguracaoDeclaracaoCertidao: getConfiguracaoDeclaracaoCertidao,
                getConfiguracaoPreview: getConfiguracaoPreview,
                selecionarTodos: selecionarTodos,
                buscarModulos: buscarModulos,
                buscarFuncionalidadePorModulo: buscarFuncionalidadePorModulo,
                ordenaLista: ordenaLista,
                buscarFundamentoLegalFuncionalidade: buscarFundamentoLegalFuncionalidade,
                getNumeroPorExtenso: getNumeroPorExtenso,
                salvar: salvar,
                atualizarDicionarioDeclaracao: atualizarDicionarioDeclaracao,
                atualizarDicionarioContrato: atualizarDicionarioContrato,
                buscarAlterarAtoContrato: buscarAlterarAtoContrato,
                buscarCodPrefixo: buscarCodPrefixo,
                buscarTodosAtosContratos: buscarTodosAtosContratos,
                filterBuscarTodosAtosContratos: filterBuscarTodosAtosContratos,
                excluirModeloAtosContratos: excluirModeloAtosContratos,
                getDicionarioFinal: getDicionarioFinal,
                setarOrdem: setarOrdem,
                getLabel: getLabel,
                atualizarModelo: atualizarModelo,
                getValorPorLabel: getValorPorLabel
            };
        }
})();

(function(){
    'use strict';
        angular
        .module('su-integrador')
        .controller('previewController',
                    ['$scope', 'escopoCompartilhadoService', '$filter', 'atosContratosService',
                    'localStorageService', 'comumIntegradorService', '$state', 'ComumFactory',
            function ($scope, escopoCompartilhadoService, $filter, atosContratosService,
                      localStorageService, comumIntegradorService, $state, ComumFactory) {

          var vm = $scope;

          vm.TIPO_CONTRATO_TERMO = 1;
          vm.TIPO_DECLARACAO_CERTIDAO = 2;

          vm.formmatarCodigoGerado = comumIntegradorService.formmatarCodigoGerado;
          vm.ordemCampos = atosContratosService.ordenaLista;
          vm.getLabel = atosContratosService.getLabel;
          vm.removerHTML = comumIntegradorService.removerHTML;


          var init = function () {

            vm.atoTermo = escopoCompartilhadoService.getObjeto('atosContratos');

            // vm.atoTermo = localStorageService.get('tijolo');

            if(!vm.atoTermo){
              $state.go('INTEGRADOR.ATOS_E_CONTRATOS');
            }

            buscarCodPrefixo();

            if(!vm.atoTermo.id){
              vm.dictConfigurarComponente = angular.copy(getDicionarioInicial());
              vm.atoTermo.configuracaoAtosContratos = angular.copy(
                                    atosContratosService.getConfiguracaoPreview());
            }else {
              vm.dictConfigurarComponente = angular.copy(vm.atoTermo.configuracaoComponentes);
            }

            if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_DECLARACAO_CERTIDAO){
              atosContratosService.atualizarDicionarioDeclaracao(vm.atoTermo, vm.dictConfigurarComponente);

            }else if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
              atosContratosService.atualizarDicionarioContrato(vm.atoTermo, vm.dictConfigurarComponente);
            }


            vm.tituloMinisterio = atosContratosService.getLabel('titulo',
                                           vm.atoTermo.configuracaoAtosContratos);
          };

          var buscarCodPrefixo = function () {
            if(angular.isDefined(vm.atoTermo.prefixo)){
              atosContratosService.buscarCodPrefixo(vm.atoTermo.prefixo).then(
                function (resposta) {
                  vm.codigoGerado = resposta.data.resposta[0];
              });
            }
          };

          var getDicionarioInicial = function(){
              if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
                  return angular
                      .copy(atosContratosService.getConfiguracaoInicialContratoTermo());

              }else if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_DECLARACAO_CERTIDAO){
                  return angular
                      .copy(atosContratosService.getConfiguracaoDeclaracaoCertidao());
              }
          };

          var mesangemSucesso = function () {
              ComumFactory.mostrarMensagemSucesso('Salvo com Sucesso.');
          };

          var atualizarModelo = function(){
            if(vm.atoTermo){
              vm.atoTermo.configuracaoComponentes = atosContratosService
                          .getDicionarioFinal(vm.dictConfigurarComponente);

              atosContratosService.setarOrdem(vm.atoTermo.configuracaoComponentes);
              atosContratosService.atualizarModelo(vm.atoTermo, vm.atoTermo.configuracaoComponentes);

              angular.forEach(vm.atoTermo.configuracaoAtosContratos, function(config){
                if(config.label == 'titulo'){
                  config.valorLabel = vm.tituloMinisterio;
                }else if(config.label == 'textoUniao'){
                  config.valorLabel = atosContratosService
                        .getValorPorLabel(vm.atoTermo.configuracaoComponentes, 'DENOMINACAO_UNIAO');
                }
              });

              escopoCompartilhadoService.setObjetos('atosContratos', vm.atoTermo);
            }
          };

          vm.voltar = function () {
            atualizarModelo();
            $state.go('INTEGRADOR.ATOS_E_CONTRATOS');
          };

          vm.salvar = function(){
            atualizarModelo();
            atosContratosService.salvar(vm.atoTermo).then(function(res){
                  mesangemSucesso();
                  escopoCompartilhadoService.limparEscopo();
                  $state.go('INTEGRADOR.CONSULTAR_ATOS_E_CONTRATOS');
            }, function(erro){
                  ComumFactory.mostrarMensagemError(erro.data.mensagem[0]);
            });
          };

          init();

        }])
})();

(function(){
  'use strict';
  angular
    .module('su-integrador')
    .controller('consultarAtosContratosTermoController',
      ['$scope', '$mdDialog', 'atosContratosService', '$q', 'FormularioFactory',
                '$state', '$filter', 'escopoCompartilhadoService',
        function ($scope, $mdDialog, atosContratosService, $q, FormularioFactory,
                $state, $filter, escopoCompartilhadoService) {

          var vm = $scope;

          vm.LIMPAR = 'LIMPAR';
          vm.PESQUISAR = 'PESQUISAR';
          vm.REMOVER = 'REMOVER';
          vm.NOVO = "NOVO";

          vm.filtro = {};
          vm.listaAtosContratos = [];

          vm.validarFormulario = FormularioFactory.validarFormularioComMensagem;

          var init = function(){
            vm.registroInicial = 1;
            vm.tamanhoLimite = 5;
            vm.totalItems = 0;

            escopoCompartilhadoService.limparEscopo();

            $q.all([atosContratosService.buscarTipoModeloContrato(),
              atosContratosService.buscarTipoCompetenciaAssinatura(),
              atosContratosService.buscarModulos()])
              .then(function(res){
                if(res[0]){
                  vm.tipoModelo = res[0].data.resposta;
                }
                if(res[1]){
                  vm.tipoCompetenciaContrato = res[1].data.resposta;
                }
                if(res[2]){
                  vm.modulos = res[2].data.resposta;
                }
              });
            buscarDadosModelos();
          };

          vm.buscarFuncionalidadePorModulo = function(){
            if(vm.filtro.modulo){
              atosContratosService.buscarFuncionalidadePorModulo(vm.filtro.modulo.id)
                .then(function(resultado){
                  vm.funcionalidades = resultado.data.resposta;
                });
            }
          };

          vm.limparCamposPesquisa = function() {
            vm.filtro = {};
          };

          vm.modalConfirmarExclusaoAtos = function ($event, idContrato) {
            var confirm = $mdDialog.confirm({focusOnOpen: false})
              .title($filter('translate')('titulo-excluir-modelo'))
              .textContent($filter('translate')('msg-confirmar-exclusao'))
              .targetEvent($event)
              .ok($filter('translate')('rb-sim'))
              .cancel($filter('translate')('rb-nao'));

            $mdDialog.show(confirm).then(function () {
                vm.abrirModalExcluirModelo($event, idContrato);
            }, function () {
                $mdDialog.cancel();
            });
          };

          vm.abrirModalExcluirModelo = function (ev, idContrato) {

            $mdDialog.show({
              controller: ["$scope", function ($scope) {
                var init = function(){
                  $scope.justificativaExclusao = '';
                };

                $scope.confirmar = function(){
                  var atosContratos = {
                    id: idContrato,
                    justificativa: $scope.justificativaExclusao
                  };

                  atosContratosService.excluirModeloAtosContratos(atosContratos).then(function(){
                    vm.buscarDadosModelosPaginacao();
                    $scope.fechar();
                  });
                };

                $scope.fechar = function () {
                  $mdDialog.cancel();
                };

                init();

                $scope.closeDialog = function () {
                  $mdDialog.cancel();
                };

              }],
              templateUrl: 'scripts-integrador/pages/atos-contratos/view/partials/modalExclusaoModelo.html',
              parent: angular.element(document.body),
              targetEvent: ev,
              clickOutsideToClose: false,
              locals: {
              }
            });
          };


          var buscarDadosModelos = function() {
            atosContratosService.buscarTodosAtosContratos((vm.registroInicial - 1), vm.tamanhoLimite)
                  .then(function(resp) {
              vm.listaAtosContratos = resp.data.resposta[0].content;
              vm.totalItems = resp.data.resposta[0].totalElements;
            });
          };

          var buscarPorFiltroDadosModelos = function() {
            var filtroAtoTermo = {
              descricao: vm.filtro.descricao ? vm.filtro.descricao : null,
              modulo: vm.filtro.modulo ? vm.filtro.modulo.constante : null,
              tipoModeloContrato: vm.filtro.tipoModeloContrato ? vm.filtro.tipoModeloContrato.constante : null,
              instrumentoFuncionalidade: vm.filtro.instrumentoFuncionalidade ?
                      vm.filtro.instrumentoFuncionalidade.tipoInstrumentoFuncionalidadeEnum : null
            };

            atosContratosService.filterBuscarTodosAtosContratos(filtroAtoTermo, (vm.registroInicial - 1), vm.tamanhoLimite)
              .then(function(resp) {
                vm.listaAtosContratos = resp.data.resposta[0].content;
                vm.totalItems = resp.data.resposta[0].totalElements;
              });
          };

          vm.buscarDadosModelosPaginacao = function() {
            if ((vm.filtro.descricao && vm.filtro.descricao != "")
              || (vm.filtro.modulo && vm.filtro.modulo != "")
              || (vm.filtro.tipoModeloContrato && vm.filtro.tipoModeloContrato != "")
              || (vm.filtro.instrumentoFuncionalidade && vm.filtro.instrumentoFuncionalidade != "")) {
              buscarPorFiltroDadosModelos();
            } else {
              buscarDadosModelos();
            }
          };

          vm.redirecionarNovoAtoContrato = function() {
            escopoCompartilhadoService.limparEscopo();
            $state.go('INTEGRADOR.ATOS_E_CONTRATOS');
          };

          init();

        }]);
})();

(function(){
    'use strict';
        angular
        .module('su-integrador')
        .controller('cadastroContratoTermoController',
                    ['$scope', '$mdDialog', 'atosContratosService', '$q', 'FormularioFactory', '$filter',
                      'ComumFactory', 'escopoCompartilhadoService', '$state', '$stateParams', 'localStorageService',
                      'instrumentoFuncionalidadeService', 'comumIntegradorService',
            function ($scope, $mdDialog, atosContratosService, $q, FormularioFactory, $filter, ComumFactory,
                      escopoCompartilhadoService, $state, $stateParams, localStorageService,
                      instrumentoFuncionalidadeService, comumIntegradorService) {


          var vm = $scope;

          vm.PRE_VISUALIZAR = 'PRE_VISUALIZAR';
          vm.EDITAR = 'EDITAR';
          vm.acaoSubmit = '';

          vm.TIPO_CONTRATO_TERMO = 1;
          vm.TIPO_DECLARACAO_CERTIDAO = 2;

          vm.isEditar = false;
          vm.removerHTML = comumIntegradorService.removerHTML;

          var init = function(){

              vm.atoTermo = escopoCompartilhadoService.getObjeto('atosContratos');

              if(vm.atoTermo){
                preencherAtosContratos();
              }else {
                escopoCompartilhadoService.limparEscopo();

                if (!vm.atoTermo && $stateParams.idContrato) {
                  atosContratosService.buscarAlterarAtoContrato($stateParams.idContrato).then(function(resp) {
                    vm.atoTermo = resp.data.resposta[0];
                    preencherAtosContratos();
                    vm.atoTermo.despacho = angular.copy(vm.atoTermo.despacho);
                    if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
                      vm.atoTermo.denominacaoPartes.uniao = angular.copy(vm.atoTermo.denominacaoPartes.uniao);
                      vm.atoTermo.denominacaoPartes.terceiro = angular.copy(vm.atoTermo.denominacaoPartes.terceiro);
                    }
                  });
                } else if(!vm.atoTermo){
                    vm.atoTermo = {
                      cadastramento: new Date()
                    };
                  }
              }

              vm.acaoSubmit = undefined;

              $q.all([atosContratosService.buscarTipoModeloContrato(),
                      atosContratosService.buscarTipoCompetenciaAssinatura(),
                      atosContratosService.buscarModulos()])
                      .then(function(res){

                      if(res[0]){
                          vm.tipoModelo = res[0].data.resposta;
                      }

                      if(res[1]){
                          vm.tipoCompetenciaContrato = res[1].data.resposta;
                      }

                      if(res[2]){
                          vm.modulos = res[2].data.resposta;
                      }

              });

              vm.configPreambulo = {
                  titulo : $filter('translate')('label-preambulo'),
                  descricao: $filter('translate')('descricao-modelo'),
                  labelEditar: $filter('translate')('editar-campo', {
                      'campo': $filter('translate')('label-preambulo')
                  }),
                  labelExcluir: $filter('translate')('excluir-campo', {
                      'campo': $filter('translate')('label-preambulo')
                  })
              };

              vm.configConteudo  = {
                  titulo : $filter('translate')('label-conteudo'),
                  descricao: $filter('translate')('descricao-modelo'),
                  labelEditar: $filter('translate')('editar-campo', {
                      'campo': $filter('translate')('label-conteudo')
                  }),
                  labelExcluir: $filter('translate')('excluir-campo', {
                      'campo': $filter('translate')('label-conteudo')
                  })
              };

          };

          var preencherAtosContratos = function () {
            vm.atoTermo.cadastramento = new Date(vm.atoTermo.cadastramento);
            vm.buscarFuncionalidadePorModulo();
            /* vm.selecionarTipo();*/
            vm.buscarDespachoPorFuncionalidade();
          };

          vm.buscarFundamentoLegalFuncionalidade = function () {
            if(vm.atoTermo.instrumentoFuncionalidade){
              atosContratosService.buscarFundamentoLegalFuncionalidade(vm.atoTermo.instrumentoFuncionalidade.id)
                  .then(function (resultado) {
                        vm.fundamentos = resultado.data.resposta;
                  });
            }
          };

          vm.buscarFuncionalidadePorModulo = function(){
              if(vm.atoTermo.modulo){
                atosContratosService.buscarFuncionalidadePorModulo(vm.atoTermo.modulo.id)
                            .then(function(resultado){
                        vm.funcionalidades = resultado.data.resposta;

                });

              }
          };

          vm.limpaModulosEInstrumentos = function(){
            vm.despacho = undefined;
            vm.atoTermo.instrumentoFuncionalidade = undefined;
          };

          vm.buscarDespachoPorFuncionalidade = function () {
            if(vm.atoTermo.instrumentoFuncionalidade){
              atosContratosService.buscarDespachoPorFuncionalidade(vm.atoTermo.instrumentoFuncionalidade.id)
                            .then(function (resultado) {
                vm.despacho = resultado.data.resposta;
              });
            }
          };
          vm.selecionarTipo = function(){
              vm.atoTermo.clausulas = undefined;
              vm.atoTermo.conteudo = undefined;
              vm.atoTermo.preambulo = undefined;
              vm.atoTermo.denominacaoPartes = undefined;

              getDicionarioInicial();
          };

          var getDicionarioInicial = function(){
              if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
                  vm.atoTermo.configuracaoComponentes = angular
                      .copy(atosContratosService.getConfiguracaoInicialContratoTermo());
                  //adicionarInformacoesDenomicacaoDasPartes(config);

              }else if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_DECLARACAO_CERTIDAO){
                  vm.atoTermo.configuracaoComponentes = angular
                      .copy(atosContratosService.getConfiguracaoDeclaracaoCertidao());
              }
          };

          vm.validarFormulario = function (form) {
            var valido = FormularioFactory.validarFormularioComMensagem(form);
             if(valido && vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
               if(!vm.atoTermo.preambulo || !vm.atoTermo.clausulas){
                 ComumFactory.mostrarMensagemError($filter('translate')('erro-campos-obrigatorio'));
                 valido = false;
               }
             }else if(valido && vm.atoTermo.tipoModeloContrato.id == vm.TIPO_DECLARACAO_CERTIDAO){
               if(!vm.atoTermo.preambulo || !vm.atoTermo.conteudos){
                 ComumFactory.mostrarMensagemError($filter('translate')('erro-campos-obrigatorio'));
                 valido = false;
               }
             }
             return valido;
          };

          vm.enviarFormulario = function(){
            if(vm.acaoSubmit == vm.PRE_VISUALIZAR){
                escopoCompartilhadoService.setObjetos('atosContratos', vm.atoTermo);

                //localStorageService.set('tijolo', vm.atoTermo)
                if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
                  $state.go('INTEGRADOR.MODELO_CONTRATO');

                }else if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_DECLARACAO_CERTIDAO){
                      $state.go('INTEGRADOR.MODELO_CERTIDAO');
                }
            }
          };

          vm.fechar = function () {
            $state.go('INTEGRADOR.CONSULTAR_ATOS_E_CONTRATOS');
          };

          init();

    }]);
})();

(function () {

  'use strict';

  directive.$inject = ["$document"];
  angular
       .module('su-integrador')
      .directive('iframeSetDimensionsOnload', directive);

    function directive($document) {

    return {
      link: function(scope, element, attrs, modelCtrl) {

            var conteudo = angular.element($document[0].querySelector('#conteudo'));
            var height = conteudo.prop('offsetHeight');
          
            var iFrameHeight = (height + 1000) + 'px';
            var iFrameWidth = '100%';

            element.css('width', iFrameWidth);
            element.css('height', iFrameHeight);
            element.css('border-width',  '0px');
      }
    };
  }
  
 })();

(function () {

  'use strict';

  directive.$inject = ["$interval", "$rootScope", "$state"];
  angular.module('su-integrador')
      .directive('capitalize', directive);

    function directive($interval, $rootScope, $state) {

    return {
      require: 'ngModel',
      link: function(scope, element, attrs, modelCtrl) {

        var capitalize = function(inputValue) {
          if (inputValue == undefined) inputValue = '';
          var capitalized = inputValue.toUpperCase();
          if (capitalized !== inputValue) {
            modelCtrl.$setViewValue(capitalized);
            modelCtrl.$render();
          }
          return capitalized;
        };

        modelCtrl.$parsers.push(capitalize);
        capitalize(scope[attrs.ngModel]);
      }
    };
  }
  
 })();

(function () {
  'use strict';

          directive.$inject = ["$mdDialog", "$filter"];
    angular
        .module('su-integrador')
        .directive('adicionarEditorHtml', directive);

  function directive($mdDialog, $filter) {
            return {
                restrict: 'EA',
                scope: {
                    config: '=',
                    model:'=ngModel'
                },
                templateUrl: 'scripts-integrador/app/directives/secoes/templates/adicionarEditorHtml.html',
                link: function ($scope) {

                    var vm = $scope;

                    vm.abrir = function (ev, itemEditar) {
                        $mdDialog.show({
                            controller: ["$scope", function ($scope) {

                                var init = function(){
                                  $scope.titulo = vm.config.titulo;
                                  $scope.descricao = vm.config.descricao;

                                  $scope.editar = (itemEditar !== undefined);
                                  $scope.item = ($scope.editar) ?
                                            angular.copy(itemEditar) : undefined;

                                };

                                var validar = function(){
                                    return ($scope.item);
                                };

                                $scope.confirmar = function(){
                                    if(validar()){
                                        vm.model = $scope.item;
                                        $scope.fechar();
                                    }
                                };

                                $scope.fechar = function () {
                                    $mdDialog.cancel();
                                };

                                init();

                            }],
                            templateUrl: 'scripts-integrador/app/directives/secoes/templates/partials/modalAdicionarEditorHtml.html',
                            parent: angular.element(document.body),
                            targetEvent: ev,
                            clickOutsideToClose: false,
                            locals: {
                            }
                        });
                };


                vm.remover = function(ev) {
                    var confirm = $mdDialog.confirm({focusOnOpen:false})
                        .title($filter('translate')('excluir'))
                        .textContent($filter('translate')('msg-excluir', {
                             campo: vm.config.titulo
                         }))
                        .targetEvent(ev)
                        .ok($filter('translate')('rb-sim'))
                        .cancel($filter('translate')('rb-nao'));
                    $mdDialog.show(confirm).then(function() {

                        vm.model = undefined;

                   }, function() {
                        $mdDialog.cancel();
                    });
                };

                vm.editar = function(ev){
                    vm.abrir(ev, vm.model);
                };

                var init = function(){
                    vm.labelEditar = vm.config.labelEditar;
                    vm.labelExcluir = vm.config.labelExcluir;
                };

                init();

                }
              };
          }
}());

(function () {

          directive.$inject = ["atosContratosService", "$mdDialog", "$filter"];
    angular
        .module('su-integrador')
        .directive('adicionarClausulas', directive);

         function directive(atosContratosService, $mdDialog, $filter) {
            return {
                restrict: 'EA',
                scope: {
                   clausulas: '=ngModel'
                },
                templateUrl: 'scripts-integrador/app/directives/secoes/templates/adicionarClausulas.html',
                link: function ($scope) {

                    var vm = $scope;


                    vm.abrir = function (ev) {
                        $mdDialog.show({
                            controller: ["$scope", function ($scope) {

                                var init = function(){
                                    $scope.ordemCampos = atosContratosService.ordenaLista;
                                    $scope.getNumeroPorExtenso = atosContratosService.getNumeroPorExtenso;
                                    $scope.listClausulas = (vm.clausulas !== undefined) ?
                                                     angular.copy(vm.clausulas) : [];
                                    $scope.adicionarClausula();
                                };

                                $scope.adicionarClausula = function(){
                                    $scope.listClausulas.push({descricao: undefined});
                                };

                                $scope.confirmar = function(){
                                  if($scope.formClausula.$invalid){
                                    return;
                                  }
                                    vm.clausulas = [];
                                    angular.forEach($scope.listClausulas, function(item, $index){
                                        if(item.descricao){
                                            item.ordem = $index;
                                            vm.clausulas.push(item);
                                        }
                                    });
                                    $scope.fechar();
                                };

                                $scope.fechar = function () {
                                    $mdDialog.cancel();
                                };

                                $scope.remover = function(index){
                                    $scope.listClausulas.splice(index, 1);
                                };

                                init();

                            }],
                            templateUrl: 'scripts-integrador/app/directives/secoes/templates/partials/modalClausulas.html',
                            parent: angular.element(document.body),
                            targetEvent: ev,
                            clickOutsideToClose: false,
                            locals: {
                            }
                        });
                    };


                    vm.remover = function(ev, index) {
                        var confirm = $mdDialog.confirm({focusOnOpen:false})
                            .title($filter('translate')('excluir'))
                            .textContent($filter('translate')('msg-excluir',  {
                                campo: $filter('translate')('label-clausulas')
                            }))
                            .targetEvent(ev)
                            .ok($filter('translate')('rb-sim'))
                            .cancel($filter('translate')('rb-nao'));
                        $mdDialog.show(confirm).then(function() {
                              vm.clausulas.splice(index, 1);
                        }, function() {
                            $mdDialog.cancel();
                        });
                    };


                    var init = function(){
                        vm.labelExcluir = $filter('translate')('excluir-campo', {
                            campo: $filter('translate')('label-clausulas')
                        });

                        vm.labelEditar = $filter('translate')('editar-campo', {
                            campo: $filter('translate')('label-clausulas')
                        });
                    };

                    init();

                }
              };
          }
}());

(function () {

  directive.$inject = ["fundamentoLegalService", "$mdDialog"];
  angular
    .module('su-integrador')
    .directive('incluirUtilizacao', directive);

  function directive(fundamentoLegalService, $mdDialog) {
    return {
      restrict: 'EA',
      scope: {
        utilizacoes: '=',
        bloquear: '=',
        isDetalhar: '='
      },
      templateUrl: 'scripts-integrador/app/directives/fundamento-legal/template/incluirUtilizacao.html',
      link: function ($scope) {

        var vm = $scope;

        vm.utilizacao = {tipoUsoPermitido: [], especificacoes: []};

        vm.listaTipoUsoPermitido = [];
        vm.listaEspecificacao = [];

        var init = function(){
          vm.utilizacoes = vm.utilizacoes || [];
          buscarTipoUsoPermitido();
          vm.buscarEspecificacoes();
        };

        vm.buscarEspecificacoes = function () {
          fundamentoLegalService.buscarTodasEspecificacoes().then(function (resp) {
            vm.especificacoes = resp.data.resposta;
          })
        };

        vm.validarExibicaoEspecificacao = function (item) {
          var qntEspecificacoesTabela = 0;
          angular.forEach(vm.utilizacoes, function (utilizacao) {
            angular.forEach(utilizacao.especificacoes, function (especificacao) {
              if (especificacao.id === item.id) {
                qntEspecificacoesTabela ++;
              }
            })
          });
          return qntEspecificacoesTabela === 0;
        };


        vm.validarExibicaoTipoPermitido = function (item) {
            var qntEspecificacoesPorTipo = 0;
            var qntEspecificacoesTabela = 0;
          angular.forEach(vm.especificacoes, function (especificacao) {
             if (especificacao.tipoUtilizacao.id === item.id) {
               qntEspecificacoesPorTipo ++;
             }
          });

          angular.forEach(vm.utilizacoes, function (utilizacao) {
            angular.forEach(utilizacao.especificacoes, function (especificacao) {
              if (especificacao.tipoUtilizacao.id === item.id) {
                qntEspecificacoesTabela ++;
              }
            })
          });
          return qntEspecificacoesPorTipo > qntEspecificacoesTabela;
        };

        vm.selecionarTodosTipo = function(todosTipos) {
          if(todosTipos){
            fundamentoLegalService.buscarTodasEspecificacoes().then(function (resp) {
              vm.utilizacao.tipoUsoPermitido = vm.listaTipoUsoPermitido;
              vm.listaEspecificacao = resp.data.resposta;
            });
          } else{
            vm.utilizacao.tipoUsoPermitido = [];
            vm.listaEspecificacao = [];
          }

        };

        vm.selecionarTodosEspecificacao = function (tipoEspecificacao) {
          if(tipoEspecificacao){
            vm.utilizacao.especificacoes = vm.listaEspecificacao;
          } else {
            vm.utilizacao.especificacoes = [];
          }
        };

        vm.adicionarListaUtilizacoes = function() {
          var existe = false;
          if (vm.utilizacao.tipoUsoPermitido && vm.utilizacao.especificacoes) {
            angular.forEach(vm.utilizacao.tipoUsoPermitido, function (tipoUsoPermitido) {
              angular.forEach(vm.utilizacoes, function (utilizacao) {
                 if (tipoUsoPermitido.id === utilizacao.tipoUsoPermitido.id) {
                   existe = true;
                   var especificacoesSelecionadas = [];
                    angular.forEach(vm.utilizacao.especificacoes, function (especificacao) {
                         if (especificacao.tipoUtilizacao.id === tipoUsoPermitido.id) {
                           especificacoesSelecionadas.push(especificacao);
                         }
                       });
                   var especificacoesTabela = utilizacao.especificacoes;
                   utilizacao.especificacoes = especificacoesTabela.concat(especificacoesSelecionadas);
                 }
              });
            });

            if (!existe) {
              angular.forEach(vm.utilizacao.tipoUsoPermitido, function (tipoUsoPermitido) {
                var especificacoes = [];
                angular.forEach(vm.utilizacao.especificacoes, function (especificacao) {
                  if (especificacao.tipoUtilizacao.id === tipoUsoPermitido.id) {
                    especificacoes.push(especificacao);
                  }
                });
                if (especificacoes.length > 0) {
                  vm.utilizacoes.push({tipoUsoPermitido: tipoUsoPermitido, especificacoes: especificacoes});
                }
              });
            }
          }
          vm.utilizacao.tipoUsoPermitido = undefined;
          vm.utilizacao.especificacoes = undefined;
        };

        var buscarTipoUsoPermitido = function () {
          fundamentoLegalService.pesquisarTodosTipoUsoPermitido().then(function (resp) {
            vm.listaTipoUsoPermitido = resp.data.resposta;
          })
        };

        vm.buscarEspecificacao = function () {
          vm.listaEspecificacao = [];
          vm.utilizacao.especificacoes = [];
          if(vm.utilizacao.tipoUsoPermitido.length < vm.listaTipoUsoPermitido.length){
            angular.forEach(vm.utilizacao.tipoUsoPermitido, function (tipoUso) {
              fundamentoLegalService.pesquisarEspecificacao(tipoUso.id).then(function (resp) {
                vm.listaEspecificacao = vm.listaEspecificacao.concat(resp.data.resposta);
              })
            })
          }
        };

        vm.removerUsoEspecificacao = function($event, index, itemUsoEspecificacao) {
          var confirm = $mdDialog.confirm({focusOnOpen: false})
            .title('Excluir')
            .textContent('Deseja remover este registro?')
            .targetEvent($event)
            .ok('Sim')
            .cancel('Não');

          $mdDialog.show(confirm).then(function () {
            itemUsoEspecificacao.especificacoes.splice(index,1);
            if (itemUsoEspecificacao.especificacoes.length === 0) {
              vm.utilizacoes.splice(indexOfTipoUsoPermitido(itemUsoEspecificacao),1);
            }
          }, function () {
            $mdDialog.cancel();
          });
        };

        function indexOfTipoUsoPermitido(itemUsoEspecificacao) {
          for (var i = 0; i < vm.utilizacoes.length; i ++) {
            if (itemUsoEspecificacao.tipoUsoPermitido.id === vm.utilizacoes[i].tipoUsoPermitido.id) {
              return i;
            }
          }

        }

        $scope.$watch('listaEspecificacao', function() {
          for(var index in vm.listaEspecificacao){
            for(var index2 in vm.utilizacoes){
              for(var index3 in vm.utilizacoes[index2].especificacoes){
                if(vm.listaEspecificacao[index].descricao === vm.utilizacoes[index2].especificacoes[index3].descricao){
                  vm.listaEspecificacao.splice(index, 1);
                }
              }
            }
          }
        });

          init();

      }
    };
  }
}());

angular.module('su-integrador')
.directive('carousel', function() {
  return {
    restrict:'EA',
    scope:{
      imagens:'='
    },
    replace:false,
    controller:["$scope", function($scope){
      $scope.selectedIndex=0;

      $scope.avanca=function(){
        if($scope.selectedIndex <=$scope.imagens.length){
          $scope.selectedIndex++;
        }
      };

      $scope.retorna=function(){
        if($scope.selectedIndex >0){
          $scope.selectedIndex--;
        }
      };

    }],
    templateUrl: 'scripts-integrador/app/directives/carousel/carousel.html'
  };
});

/**
 * Created by Basis Tecnologia on 27/10/2016.
 */
(function(){

  'use strict';

  menuService.$inject = ["$rootScope", "localStorageService"];
  angular
    .module('su-integrador')
    .service('menuService', menuService);

  function menuService($rootScope, localStorageService) {

    var ADMINSTRACAO = 'INTEGRADOR.ADMINISTRACAO';   

    function getNomePaginaAtual (rota) {
      var menus = localStorageService.get('menu');
      var menusDestinacao = getMenu(menus);
      if (menusDestinacao) {
        getNomePaginaItemMenu(menusDestinacao, rota);
      }

    }

    function getNomePaginaItemMenu (menu, rota) {
      if (menu.codigoIdentificador === rota) {
        $rootScope.paginaAtual = menu.descricaoResumida;
        localStorageService.set('menuAnterior', $rootScope.paginaAtual);
      } else {
        $rootScope.paginaAtual =  localStorageService.get('menuAnterior');
        lerItemMenu(menu.nos, rota);
      }
    }

    function lerItemMenu(menus, rota) {
      if (menus) {
        for (var i = 0; i < menus.length; i++) {
          getNomePaginaItemMenu(menus[i], rota);
        }
      }

    }

    function getMenu(menus) {
      var indice = undefined;
      if (menus) {
        for (var i = 0; i < menus.length; i++) {
          if (ADMINSTRACAO == menus[i].codigoIdentificador) {
            indice = i;
            break;
          }
        }
        return menus[indice];
      }
      return undefined;
    }

    return {
      getNomePaginaAtual: getNomePaginaAtual
    }
  }

})();
(function () {

  'use strict';

  angular
    .module('su-integrador')
    .factory('integradorEscopoCompartilhadoService', service);

  function service () {
    var _objetos = {};

    var _urlsPorAmbiente = {};

    function setObjetos (nomeObjeto, objeto) {
      _objetos[nomeObjeto] = objeto;
    }

    function getObjeto(nomeObjeto) {
      return _objetos[nomeObjeto];
    }

    function setUrlsPorAmbiente(urls) {
      return _urlsPorAmbiente = urls;
    }

    function getUrlsPorAmbiente() {
      return _urlsPorAmbiente;
    }

    function limparEscopo(){
      _objetos = {};
    }

    return {
      setObjetos: setObjetos,
      getObjeto: getObjeto,
      setUrlsPorAmbiente: setUrlsPorAmbiente,
      getUrlsPorAmbiente: getUrlsPorAmbiente,
      limparEscopo: limparEscopo
    };
  }

})();

(function(){

  'use strict';

  angular
    .module('su-integrador')
    .service('formularioIntegradorService', ["ComumFactory", "$filter", function (ComumFactory,$filter) {

      var instance = function () {


        var verificarValidade = function (form) {
          return !form.$valid;
        };

        var verificarObrigatoriedade = function (form) {

          var error = form.$error;
          var invalido = false;

          angular.forEach(error.required, function (field) {
            if (field.$invalid) {

              invalido = true;

            }
          });

          return invalido;
        };

        this.validarFormulario = function (form) {

          form.$setSubmitted();
          var invalido = verificarObrigatoriedade(form);

          if (invalido) {
            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
          }

          return !invalido;
        };


      };

      return {
        getInstance: function () {
          return new instance();
        }
      }

    }])
})();

/**
 * Created by Basis Tecnologia on 27/10/2016.
 */
(function(){

  'use strict';

  service.$inject = ["$http", "$translate", "$rootScope", "localStorageService"];
  angular
    .module('su-integrador')
    .service('comumIntegradorService', service);

  function service($http,$translate,$rootScope, localStorageService) {


    var meses = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho',
                  'Agosto','Setembro','Outubro','Novembro','Dezembro']

    var formmatarCodigoGerado = function (codigoGerado) {
      if(angular.isDefined(codigoGerado)){

        var prefixo = codigoGerado.prefixo;
        var numero = codigoGerado.sequencial;
        var ano = codigoGerado.ano;

        return prefixo + lPad(numero, 4) + '/' + ano;
      }
    };


    var removerHTML = function(texto) {
         return texto.replace(/<.*?>/g, '');
    };

    var lPad = function (number, digits) {
      return Array(Math.max(digits - String(number).length + 1, 0)).join(0) + number;
    };

    var diaExtenso = function () {

      var data = new Date();
      var dia = data.getDate();
      var mes = data.getMonth();
      var ano = data.getYear();

      if (navigator.appName == "Netscape") {
        ano = ano + 1900;
      }

      var diaext = dia + ' de ' + meses[mes] + ' de ' + ano;
      return diaext;

    };

    return {
      lPad: lPad,
      formmatarCodigoGerado: formmatarCodigoGerado,
      diaExtenso: diaExtenso,
      removerHTML: removerHTML,

    };
  }

})();

(function () {
  'use strict';

  service.$inject = ["Upload", "$http"];
  function service(Upload, $http) {

    var URL = '/integrador/api/arquivo/';

    var apagar = function (id) {
      return $http.delete(URL + id);
    };

    var uploadArquivo = function($file){
      if ($file.id){
        return $http.post(URL + 'atualizar', $file);
      } else {
          return Upload.upload({
            url: URL + 'upload',
            data: {
              file: $file
            }
          });
      }

    };

    var gerarPreviewImagens = function($file, callback){
      if(!$file){
        return;
      }

      var reader  = new FileReader();

      reader.addEventListener("load", function () {
        (callback || angular.noop)(reader.result);
      }, false);

      reader.readAsDataURL($file);
    };

    var gerarSRCImagemBase64 = function(imagem) {
      return 'data:' + consultarExtencao(imagem.extencao) + ';base64,' + imagem.binario;
    };


    var consultarExtencao = function(extencao) {
      if (extencao == 'image/png' || extencao == '.png') {
        return 'image/png';
      } else if (extencao == 'image/tif' || extencao == '.tif') {
        return 'image/tif';
      } else if (extencao == 'image/jpeg' || extencao == '.jpeg') {
        return 'image/jpeg';
      } else if (extencao == 'image/jpg' || extencao == '.jpg') {
        return 'image/jpg';
      }
    };

    return {
      apagar: apagar,
      uploadArquivo: uploadArquivo,
      gerarPreviewImagens: gerarPreviewImagens,
      gerarSRCImagemBase64: gerarSRCImagemBase64,
      consultarExtencao: consultarExtencao
    };

  }

  angular
    .module('su-integrador')
    .factory('arquivoServiceIntegrador', service);
})();

/**
 * Created by Basis Tecnologia on 15/06/2016.
 */
(function () {
    'use strict';


angular.module("su-integrador").filter('cpfcnpj', function() {
    return function(input){
        if (!input) {
            return;
        } else if(input.toString().length <= 11 && input.toString().length > 3) {
            while(input.toString().length < 11){ input = '0'+input }
            return input.toString().substr(0,3) + '.' + input.toString().substr(3,3) + '.' + input.toString().substr(6,3) + '-' + input.toString().substr(9,2);
        }
        else {
            while(input.toString().length < 14){ input = '0'+input }
            return input.toString().substr(0,2) + '.' + input.toString().substr(2,3) + '.' + input.toString().substr(5,3) + '/' + input.toString().substr(8,4) + '-' + input.toString().substr(12,2);
        }
    };
});


})();

(function () {

  'use strict';

  angular.module('su-integrador').config(["$stateProvider", "$urlRouterProvider", function ($stateProvider, $urlRouterProvider) {

    $urlRouterProvider.otherwise('/integrador');

    $stateProvider
      .state('INTEGRADOR', {
        abstract: true,
        template: '<div id="su-integrador" ui-view></div>',
        url: '/integrador',
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/app/language');
          }]
        }
      })
      .state('INTEGRADOR.HOME', {
        url: '',
        templateUrl: 'scripts-integrador/pages/home/view/home.tmpl.html',
        controller: 'HomeIntegradorController as homeCtrl',
        ncyBreadcrumb: {
          label: 'HOME'
        }
      })
      .state('INTEGRADOR.INFORMES', {
        url: '/informes',
        templateUrl: 'scripts-integrador/pages/informes/view/consultarInformes.html',
        controller: 'informesController as informesCtrl',
        ncyBreadcrumb: {
          label: 'INFOMES',
          parent: 'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/informes/language');
          }]
        }
      })
      .state('INTEGRADOR.CADASTRAR_INFORME', {
        url: '/cadastroInformes',
        templateUrl: 'scripts-integrador/pages/informes/view/cadastroInformes.html',
        controller: 'cadastroInformesController as cadastroInformesCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRAR INFORME',
          parent: 'INTEGRADOR.INFORMES'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/informes/language');
          }]
        }
      })
      .state('INTEGRADOR.ATOS_E_CONTRATOS', {
        url: '/cadastroContratoTermo',
        templateUrl: 'scripts-integrador/pages/atos-contratos/view/cadastroContratoTermo.html',
        controller: 'cadastroContratoTermoController as cadastroContratoTermoCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRAR ATOS E CONTRATOS',
          parent: 'INTEGRADOR.CONSULTAR_ATOS_E_CONTRATOS'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/atos-contratos/language');
          }]
        }
      })
      .state('INTEGRADOR.CONSULTAR_ATOS_E_CONTRATOS', {
        url: '/consultarContratoTermo',
        templateUrl: 'scripts-integrador/pages/atos-contratos/view/consultarAtosContratosTermo.html',
        controller: 'consultarAtosContratosTermoController as consultarContratoTermoCtrl',
        ncyBreadcrumb: {
          label: 'CONSULTAR ATOS E CONTRATOS',
          parent: 'INTEGRADOR.HOME'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/atos-contratos/language');
          }]
        }
      })
      .state('INTEGRADOR.EDITAR_ATOS_E_CONTRATOS', {
        url: '/alterarContratoTermo?idContrato',
        templateUrl: 'scripts-integrador/pages/atos-contratos/view/cadastroContratoTermo.html',
        controller: 'cadastroContratoTermoController as cadastroContratoTermoCtrl',
        ncyBreadcrumb: {
          label: 'EDITAR ATOS E CONTRATOS',
          parent: 'INTEGRADOR.CONSULTAR_ATOS_E_CONTRATOS'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/atos-contratos/language');
          }]
        }
      })
      .state('INTEGRADOR.MODELO_CONTRATO', {
        url: '/modeloContrato?idContrato',
        templateUrl: 'scripts-integrador/pages/atos-contratos/view/modeloContrato.html',
        controller: 'previewController as previewCtrl',
        ncyBreadcrumb: {
          label: 'MODELO CONTRATO',
          parent: 'INTEGRADOR.ATOS_E_CONTRATOS'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/atos-contratos/language');
          }]
        }
      })
      .state('INTEGRADOR.MODELO_CERTIDAO', {
        url: '/modeloCertidao?idContrato',
        templateUrl: 'scripts-integrador/pages/atos-contratos/view/modeloCertidao.html',
        controller: 'previewController as previewCtrl',
        ncyBreadcrumb: {
          label: 'MODELO CERTIDAO',
          parent: 'INTEGRADOR.ATOS_E_CONTRATOS'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/atos-contratos/language');
          }]
        }
      })
      .state('INTEGRADOR.SIAPA', {
        url: '/siapa?url',
        templateUrl: 'scripts-integrador/pages/siapa/view/siapa.tmpl.html',
        controller: 'SiapaController as siapaCtrl',
        ncyBreadcrumb: {
          label: 'SIAPA',
          parent: 'INTEGRADOR.HOME'
        },
        params: {
          urlMenu:null
        }
      })
      .state('INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE', {
        url: '/consultaInstrumentoFuncionalidade',
        templateUrl: 'scripts-integrador/pages/instrumento-funcionalidade/view/consultaInstrumentoFuncionalidade.html',
        controller: 'consultaInstrumentoFuncionalidadeController as consultaInstrumentoFuncionalidadeCtrl',
        ncyBreadcrumb: {
          label: 'CONSULTAR INSTRUMENTO FUNCIONALIDADE',
          parent: 'INTEGRADOR.HOME'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/instrumento-funcionalidade/language');
          }]
        }
      })
      .state('INTEGRADOR.CADASTRAR_INSTRUMENTO_FUNCIONALIDADE', {
        url: '/cadastroInstrumentoFuncionalidade',
        templateUrl: 'scripts-integrador/pages/instrumento-funcionalidade/view/cadastroInstrumentoFuncionalidade.html',
        controller: 'cadastroInstrumentoFuncionalidadeController as cadastroInstrumentoFuncionalidadeCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRAR INSTRUMENTO FUNCIONALIDADE',
          parent: 'INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/instrumento-funcionalidade/language');
          }]
        }
      })

      .state('INTEGRADOR.EDITAR_INSTRUMENTO_FUNCIONALIDADE', {
        url: '/editarInstrumentoFuncionalidade/',
        templateUrl: 'scripts-integrador/pages/instrumento-funcionalidade/view/cadastroInstrumentoFuncionalidade.html',
        controller: 'cadastroInstrumentoFuncionalidadeController as cadastroInstrumentoFuncionalidadeCtrl',
        ncyBreadcrumb: {
          label: 'EDITAR INSTRUMENTO FUNCIONALIDADE',
          parent: 'INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/instrumento-funcionalidade/language');
          }]
        }
      })


      .state('INTEGRADOR.CADASTRO_DOCUMENTO',{
        url: '/cadastrarDocumento',
        templateUrl: 'scripts-integrador/pages/documento/view/cadastroDocumento.html',
        controller: 'cadastroDocumentoController as cadastroDocumentoCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRAR DOCUMENTO',
          parent: 'INTEGRADOR.CONSULTAR_DOCUMENTO_PESSOA'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/documento/language');
          }]
        }
      })

      .state('INTEGRADOR.EDITAR_DOCUMENTO',{
        url: '/editarDocumento',
        templateUrl: 'scripts-integrador/pages/documento/view/cadastroDocumento.html',
        controller: 'cadastroDocumentoController as cadastroDocumentoCtrl',
        ncyBreadcrumb: {
          label: 'EDITAR DOCUMENTO',
          parent: 'INTEGRADOR.CONSULTAR_DOCUMENTO_PESSOA'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/documento/language');
          }]
        }
      })

      .state('INTEGRADOR.CONSULTAR_DOCUMENTO_PESSOA',{
        url: '/consultarDocumento',
        templateUrl: 'scripts-integrador/pages/documento/view/consultaDocumentosPessoa.html',
        controller: 'consultarDocumentoPessoaController as consultarDocumentoPessoaCtrl',
        ncyBreadcrumb: {
          label: 'CONSULTAR DOCUMENTO PESSOA',
          parent: 'INTEGRADOR.HOME'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/documento/language');
          }]
        }
      })

      .state('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL',{
        url: '/fundamentoLegal',
        templateUrl: 'scripts-integrador/pages/fundamento-legal/view/consultarFundamentoLegal.html',
        controller: 'consultarFundamentoLegalController as consultarFundamentoLegalCtrl',
        ncyBreadcrumb: {
          label: 'CONSULTAR FUNDAMENTO LEGAL',
          parent: 'INTEGRADOR.HOME'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/fundamento-legal/language');
          }]
        }
      })

      .state('INTEGRADOR.CADASTRAR_FUNDAMENTO_LEGAL',{
        url: '/cadastrarFundamentoLegal',
        templateUrl: 'scripts-integrador/pages/fundamento-legal/view/cadastrarFundamentoLegal.html',
        controller: 'cadastrarFundamentoLegalController as cadastrarFundamentoLegalCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRAR FUNDAMENTO LEGAL',
          parent: 'INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/fundamento-legal/language');
          }]
        }
      })

      .state('INTEGRADOR.SUSPENDER_INSTRUMENTO',{
        url: '/suspenderInstrumentoFuncionalide',
        templateUrl: 'scripts-integrador/pages/instrumento-funcionalidade/view/partials/suspenderInstrumento.html',
        controller:'suspenderInstrumentoController as suspenderInstrumentoCtrl',
        ncyBreadcrumb: {
          label: 'SUSPENDER INSTRUMENTO FUNCIONALIDADE',
          parent: 'INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/instrumento-funcionalidade/language')
          }]
        }
      })

      .state('INTEGRADOR.TEXTOS_AJUDA',{
        url: '/textosAjuda',
        templateUrl: 'scripts-integrador/pages/textos-ajuda/view/manutencaoTextosAjuda.html',
        controller: 'textosAjudaController as textosAjudaCtrl',
        ncyBreadcrumb: {
          label: 'MANUTENÇÃO TEXTOS AJUDA',
          parent: 'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/textos-ajuda/language');
          }]
        }
      })

      .state('INTEGRADOR.CADASTRO_TEXTOS_AJUDA',{
        url: '/cadastroTextosAjuda',
        templateUrl: 'scripts-integrador/pages/textos-ajuda/view/cadastroTextosAjuda.html',
        controller: 'textosAjudaController as textosAjudaCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRO TEXTOS AJUDA',
          parent: 'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/textos-ajuda/language');
          }]
        }
      })

      .state('INTEGRADOR.EDITAR_TEXTOS_AJUDA',{
        url: '/edicaoTextosAjuda/:id',
        templateUrl: 'scripts-integrador/pages/textos-ajuda/view/cadastroTextosAjuda.html',
        controller: 'textosAjudaController as textosAjudaCtrl',
        ncyBreadcrumb: {
          label: 'EDIÇÃO TEXTOS AJUDA',
          parent: 'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/textos-ajuda/language');
          }]
        }
      })

      .state('error',{
        url: '/error',
        templateUrl: 'scripts-integrador/pages/geral/view/usuario-erro-login.tmpl',
      })

      .state('INTEGRADOR.CANCELAR_FUNDAMENTO_LEGAL',{
        url: '/cancelarFundamentoLegal',
        templateUrl: 'scripts-integrador/pages/fundamento-legal/view/partial/cancelarFundamentoLegal.html',
        controller: 'cancelarFundamentoLegalController as cancelarFundamentoLegalCtlr',
        ncyBreadcrumb:{
          label:'CANCELAR FUNDAMENTO LEGAL',
          parent:'INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/fundamento-legal/language');
          }]
        }

      })

      .state('INTEGRADOR.EDITAR_FUNDAMNETO_LEGAL',{
        url: '/editarFundamentoLegal',
        templateUrl: 'scripts-integrador/pages/fundamento-legal/view/cadastrarFundamentoLegal.html',
        controller: 'cadastrarFundamentoLegalController as cadastrarFundamentoLegalCtrl',
        ncyBreadcrumb: {
          label: 'EDITAR FUNDAMENTO LEGAL',
          parent: 'INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/fundamento-legal/language');
          }]
        }
      })

      .state('INTEGRADOR.DETALHAR_FUNDAMENTO_LEGAL',{
        url: '/detalharFundamentoLegal',
        templateUrl: 'scripts-integrador/pages/fundamento-legal/view/cadastrarFundamentoLegal.html',
        controller: 'cadastrarFundamentoLegalController as cadastrarFundamentoLegalCtrl',
        ncyBreadcrumb: {
          label: 'DETALHAR FUNDAMENTO LEGAL',
          parent: 'INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/fundamento-legal/language');
          }]
        }
      })

      .state('INTEGRADOR.CADASTRO_PROCEDIMENTO_OPERACIONAL_PADRAO',{
        url: '/cadastroProcedimentoOperacionalPadrao',
        templateUrl: 'scripts-integrador/pages/procedimento-operacional-padrao/view/procedimentoOperacionalPadrao.html',
        controller: 'procedimentoOperacionalPadraoController as popController',
        ncyBreadcrumb:{
          label:'CADASTRAR PROCEDIMENTO OPERACIONAL PADRÃO',
          parent:'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/procedimento-operacional-padrao/language');
          }]
        }

      })

      .state('INTEGRADOR.CADASTRO_ORIENTACAO_TECNICA',{
        url: '/orientacaoTecnica/cadastrar',
        templateUrl: 'scripts-integrador/pages/orientacao-tecnica/view/orientacaoTecnica.html',
        controller: 'orientacaoTecnicaController as orientacaoTecnicaController',
        ncyBreadcrumb:{
          label:'CADASTRAR ORIENTAÇÃO TÉCNICA',
          parent:'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/procedimento-operacional-padrao/language');
          }]
        }

      })

      .state('INTEGRADOR.CONSULTA_ORIENTACAO_TECNICA',{
        url: '/orientacaoTecnicas/consultar',
        templateUrl: 'scripts-integrador/pages/consulta-orientacao-tecnica/view/consultaOrientacaoTecnica.html',
        controller: 'consultaOrientacaoTecnicaController as consultaOrientacaoTecnicaController',
        ncyBreadcrumb:{
          label:'CONSULTAR ORIENTAÇÃO TÉCNICA',
          parent:'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/consulta-orientacao-tecnica/language');
          }]
        }

      })

      .state('INTEGRADOR.ORIENTACAO_TECNICA',{
        url: '/orientacaoTecnica/consultar/:id',
        templateUrl: 'scripts-integrador/pages/orientacao-tecnica/view/orientacaoTecnica.html',
        controller: 'orientacaoTecnicaController as orientacaoTecnicaController',
        ncyBreadcrumb:{
          label:'CONSULTAR ORIENTAÇÃO TÉCNICA',
          parent:'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/orientacao-tecnica/language');
          }]
        }

      })

      .state('INTEGRADOR.EDITAR_ORIENTACAO_TECNICA',{
        url: '/orientacaoTecnica/editar/:id',
        templateUrl: 'scripts-integrador/pages/orientacao-tecnica/view/orientacaoTecnica.html',
        controller: 'orientacaoTecnicaController as orientacaoTecnicaController',
        ncyBreadcrumb:{
          label:'EDITAR ORIENTAÇÃO TÉCNICA',
          parent:'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/orientacao-tecnica/language');
          }]
        }

      })

  }]);

})();

(function () {
  'use strict';

  angular.module('su-integrador')
    .value('urlPrefixo', '/integrador');

})();

angular.module("su-integrador").run(["$templateCache", function($templateCache) {$templateCache.put("scripts-integrador/app/language/pt-BR.json","{ \"btn-novo\": \"Novo\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-retornar\": \"Retornar\", \"btn-gravar\": \"Gravar\", \"btn-adiconar-documento\": \"Adicionar Documento\", \"btn-editar-documento\": \"Editar Documento\", \"btn-fechar\": \"Fechar\", \"btn-inserir\": \"Inserir\", \"btn-alterar\": \"Alterar\", \"btn-confirmar\": \"Confirmar\", \"btn-incluir\": \"Incluir\", \"btn-enviar\": \"Enviar\", \"rb-sim\": \"Sim\", \"rb-nao\": \"Não\", \"rb-atuar-processo\": \"Atuar processo\", \"rb-retornar-pendencia\": \"Retornar pendência para requerente\", \"rb-cancelar-requerimento\": \"Cancelar requerimento\", \"rb-processo-existente\": \"Processo existente\", \"rb-criar-novo-processo\": \"Criar novo processo\", \"label-descricao\": \"Descrição\", \"label-justificativa\": \"Justificativa\", \"label-nome\": \"Nome\", \"label-acoes\": \"Ações\", \"label-tipo\": \"Tipo\", \"label-avaliacao-tecnica\": \"Avaliação Técnica\", \"label-data\": \"Data\", \"label-situacao\": \"Situação\", \"label-responsavel\": \"Responsável\", \"label-justificativa-anotacao\": \"Justificativa/Anotação\", \"label-historico\": \"Histórico\", \"label-analise-previa-check-list\": \"Análise Prévia (Checklist)\", \"label-atesto-devidos-fins\": \"Atesto para os defidos fins que:\", \"label-documento-adicionados-requerimento\": \"Os documentos adicionados no requerimento enviado são considerados válidos para a abertura do processo\", \"label-obrigatorios\": \"Obrigatórios\", \"label-complementares\": \"Complementares\", \"label-numero-processo\": \"Nº processo\", \"label-anotacoes-complementares\": \"Anotações Complementares (caso necessário)\", \"label-denominacao\": \"Denominação das Partes\", \"label-uniao\": \"União\", \"label-terceiro\": \"Terceiro\", \"label-extensoes\": \"Extensões\", \"label-vinculo\": \"Vínculo\", \"tooltip-limpar-campos\": \"Limpar\", \"tooltip-pesquisar\": \"Pesquisar\", \"label-erro\": \"Erro\", \"label-utilizacao\": \"Utilização\", \"label-tipo-uso-permitido\": \"Tipo de Uso Permitido\", \"label-especificacao\": \"Especificação\", \"label-adicionar\": \"Adicionar\", \"label-acao\": \"Ação\", \"msg-validacao-justificativa\" : \"A justificativa é obrigatória quando é negado um documento ou análise.\", \"msg-campos-obrigatorios\" : \"Preencha os campos obrigatórios.\", \"msg-analise-salva-sucesso\" : \"Análise salva com sucesso.\", \"selecione-combo\": \"-- Selecione --\", \"erro-campos-obrigatorio\": \"Necessário preencher os dados obrigatórios.\", \"ver-campos\": \"Ver Campos\", \"ocultar-campos\": \"Ocultar Campos\", \"msg-excluir\": \"Deseja realmente excluir {{campo}}?\", \"msg-confirmar-exclusao\": \"Deseja confirmar exclusão?\", \"mensagem-campos-obrigatorios\":\"Necessário preencher os campos obrigatórios\", \"mensagem-sucesso-documentos\":\"Dados gravados com sucesso\" }");
$templateCache.put("scripts-integrador/app/directives/carousel/carousel.html","<div layout=column ng-show=\"imagens.length > 0\" class=carousel><div flex=100 layout=row><div flex=15 class=\"item volta\"><md-button ng-disabled=\"selectedIndex==0\" ng-click=retorna() class=md-icon-button><ng-md-icon icon=navigate_before size=30></ng-md-icon></md-button></div><div align=center flex=70 class=imagem><img ng-src={{imagens[selectedIndex]}}></div><div flex=15 class=\"item avanca\"><md-button ng-disabled=\"selectedIndex==imagens.length-1\" ng-click=avanca() class=md-icon-button><ng-md-icon icon=navigate_next size=30></ng-md-icon></md-button></div></div><div layout=row flex class=bolas><div align=center ng-repeat=\"i in imagens track by $index\"><ng-md-icon icon=\"{{$index==selectedIndex ? \'lens\':\'panorama_fisheye\'}}\" size=10></ng-md-icon></div></div></div><style>carousel{\n    heigh:500px;    \n}\ncarousel .item{\n    display: flex;\n    align-items: center;\n}\ncarousel .item.volta{\n    float:left;\n}\ncarousel .item.avanca{\n    float:right;\n}\ncarousel img{\n    width:80%;\n}\ncarousel .bolas{\n    margin: 0 auto;\n}</style>");
$templateCache.put("scripts-integrador/pages/atos-contratos/language/pt-BR.json","{ \"nome-ato-termo\": \"Nome do Ato/Termo\", \"label-data-cadastramento\": \"Data Cadastramento\", \"label-modulo\": \"Módulo\", \"label-tipo\": \"Tipo\", \"label-funcionalidade\": \"Instrumento/Funcionalidade\", \"label-prefixo\": \"Prefixo do código do ato\", \"label-despacho\": \"Selecionar despacho(s) vinculado(s) ao modelo\", \"label-competencia\": \"Competência para Assinatura\", \"possui-validade\": \"Possui Validade\", \"label-prazo\": \"Prazo(dias)\", \"label-clausulas\": \"Cláusulas\", \"label-conteudo\": \"conteúdo\", \"label-acoes\": \"Ações\", \"label-informe-conteudo\": \"Informe o conteúdo que irá compor o modelo\", \"label-fundamento\": \"fundamento\", \"label-preambulo\": \"Preâmbulo\", \"label-justificativa\": \"Justificativa\", \"label-checklist-configurar-componetes\": \"Configurar Componentes\", \"label-informacoes-compor-modelo\": \"Selecione a ordem e as informações e que irão compor o modelo\", \"label-contrato-termo\": \"Contrato/Termo\", \"label-ministerio-planejamento\": \"MINISTÉRIO DO PLANEJAMENTO, DESENVOLVIMENTO E GESTÃO\", \"label-secretaria-uniao\": \"SECRETARIA DO PATRIMÔNIO DA UNIÃO\", \"label-editar-modelo\": \"Editar modelo\", \"label-excluir-modelo\": \"Excluir modelo\", \"label-despacho\": \"Selecionar despacho(s) vinculados ao modelo\", \"fundamento-legal\": \"Fundamento Legal\", \"codigo-atendimento\": \"Código do Atendimento/Nº do Processo\", \"qualificacao-partes\": \"Qualificação das Partes\", \"uniao-intermedio\": \"União, por intermédio da Secretaria do Patrimônio da União, representado neste ato por\", \"tooltip-fechar\": \"Fechar\", \"tooltip-voltar\": \"Voltar\", \"tooltip-pre-visualizar\": \"Pré-Visualizar\", \"tooltip-salvar\": \"Salvar\", \"tooltip-config-componentes\": \"Configurar Componentes\", \"tooltip-adicionar\": \"Adicionar\", \"tooltip-limpar-campos\": \"Limpar campos\", \"tooltip-pesquiser-modelo\": \"Pesquisar modelo\", \"tooltip-novo-modelo\": \"Novo modelo\", \"btn-fechar\": \"fechar\", \"btn-pre-visualizacao\": \"pré-visualização\", \"btn-salvar\": \"salvar\", \"btn-config-componentes\": \"configurar componentes\", \"btn-adicionar\": \"adicionar\", \"btn-voltar\": \"Voltar\", \"clausula\": \"Cláusula\", \"clausula-identificador\": \"Clausula\", \"excluir\": \"Excluir\", \"editar-campo\": \"Editar {{campo}}\", \"excluir-campo\": \"Excluir {{campo}}\", \"descricao-modelo\": \"Informe o preambulo que irá compor o modelo:\", \"cpf\": \"cpf\", \"imovel\": \"Imóvel\", \"prazo\": \"prazo\", \"finalidade\": \"finalidade\", \"encargos\": \"encargo(s)\", \"preco-condicao-pagamento\": \"preço e condição de pagamento\", \"memorial-descritivo\": \"memorial descritivo\", \"responsavel-atual\": \"Responsável Atual\", \"texto-certidao\": \"Esta certidão/declaração é fornecida gratuitamente, e sua autenticidade poderá ser confirmada no endereço\", \"titulo-excluir-modelo\": \"Excluir Modelo\" }");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/cadastroContratoTermo.html","<md-content flex style=\"overflow: inherit !important\"><titulo-servico ng-if=menuAcessos></titulo-servico><form name=form ng-submit=\"validarFormulario(form) && enviarFormulario()\" novalidate><md-content flex style=\"overflow: inherit !important\"><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=85><label>{{\'nome-ato-termo\' | translate}}:</label><input ng-model=atoTermo.descricao ng-required=true name=\"{{\'nome-ato-termo\' | translate}}\" type=text></md-input-container><md-input flex style=\"text-align: right\"><h4 class=data-cadastramento>{{\'label-data-cadastramento\' | translate}}:</h4><md-datepicker ng-model=atoTermo.cadastramento disabled></md-datepicker></md-input></div><div layout=row layout-xs=column><md-input-container flex=20><label>{{\'label-tipo\' | translate}}:</label><md-select ng-model=atoTermo.tipoModeloContrato ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-tipo\' | translate}}\" ng-change=selecionarTipo()><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in tipoModelo | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=20 class=md-block><label>{{\'label-modulo\' | translate}}:</label><md-select ng-model=atoTermo.modulo ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=\"buscarFuncionalidadePorModulo(); limpaModulosEInstrumentos()\" name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in modulos | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=30><label>{{\'label-funcionalidade\' | translate}}:</label><md-select ng-model=atoTermo.instrumentoFuncionalidade ng-required=true ng-change=\"buscarDespachoPorFuncionalidade(); buscarFundamentoLegalFuncionalidade()\" ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-funcionalidade\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in funcionalidades | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=30><label>{{\'label-prefixo\' | translate}}:</label><input ng-model=atoTermo.prefixo maxlength=3 ng-pattern=/^[a-zA-Z]*$/ type=text capitalize ng-required=true></md-input-container></div><div layout=row layout-xs=column flex ng-if=atoTermo.instrumentoFuncionalidade><md-radio-group ng-model=atoTermo.despacho.id><label>{{\'label-despacho\' | translate}}:</label><md-radio-button ng-repeat=\"d in despacho | orderBy : \'id\'\" class=md-primary ng-value=d.id ng-disabled=d.selecionado flex-offset=5><label>{{d.descricao}}</label><md-tooltip ng-show=d.selecionado md-direction=top>Usado por {{d.nome}}</md-tooltip></md-radio-button></md-radio-group></div><div layout=row layout-xs=column flex=100 ng-if=atoTermo.instrumentoFuncionalidade><md-input-container flex><label>Fundamento legal</label><md-select ng-model=atoTermo.fundamentoLegal ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option value={{null}} ng-if=\"\'$value.id\' > 1\">{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in fundamentos | orderBy:\'descricao\'\" ng-value={{tipo}} ng-if=\"\'$value.id\' > 1\">{{removerHTML(tipo.descricao)}}</md-option><md-option ng-repeat=\"(index,tipo) in fundamentos | orderBy:\'descricao\'\" ng-value={{tipo}} ng-selected=\"index == 0\">{{removerHTML(tipo.descricao)}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex=100><md-input-container flex=60><label>{{\'label-competencia\' | translate}}:</label><md-select ng-model=atoTermo.tipocompetenciaAssinatura ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-competencia\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in tipoCompetenciaContrato | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container ng-if=\"atoTermo.tipoModeloContrato.id == TIPO_DECLARACAO_CERTIDAO\" flex=20 flex-offset=5><md-checkbox ng-model=atoTermo.validade class=md-primary>{{\'possui-validade\' | translate}}</md-checkbox></md-input-container><md-input-container ng-if=\"atoTermo.tipoModeloContrato.id == TIPO_DECLARACAO_CERTIDAO\n                                        && atoTermo.validade\" flex=20><label>{{\'label-prazo\' | translate}}:</label><input ng-model=atoTermo.prazo ng-required=atoTermo.validade name=\"{{\'label-prazo\' | translate}}\" type=number></md-input-container></div><span ng-if=atoTermo.tipoModeloContrato><md-content flex ng-if=\"atoTermo.tipoModeloContrato.id == TIPO_CONTRATO_TERMO\"><h3>{{\'label-denominacao\' | translate}}</h3><div layout=row layout-sm=column><md-input-container class=\"md-block sem-margem\" flex-gt-xs flex><label>{{\'label-uniao\' | translate}}</label><input ng-model=atoTermo.denominacaoPartes.uniao ng-required=true type=text></md-input-container><md-input-container class=\"md-block sem-margem\" flex-gt-xs flex><label>{{\'label-terceiro\' | translate}}</label><input ng-model=atoTermo.denominacaoPartes.terceiro ng-required=true type=text></md-input-container></div></md-content><md-content flex><md-tabs md-dynamic-height md-border-bottom><md-tab label=\"{{\'label-clausulas\' | translate | uppercase}}*\" ng-if=\"atoTermo.tipoModeloContrato.id == TIPO_CONTRATO_TERMO\"><md-content class=md-padding><adicionar-clausulas ng-model=atoTermo.clausulas></adicionar-clausulas></md-content></md-tab><md-tab label=\"{{\'label-conteudo\' | translate | uppercase}}*\" ng-if=\"atoTermo.tipoModeloContrato.id == TIPO_DECLARACAO_CERTIDAO\"><md-content class=md-padding><adicionar-editor-html ng-model=atoTermo.conteudos config=configConteudo></adicionar-editor-html></md-content></md-tab><md-tab label=\"{{\'label-preambulo\' | translate | uppercase}}*\"><md-content class=md-padding><adicionar-editor-html ng-model=atoTermo.preambulo config=configPreambulo></adicionar-editor-html></md-content></md-tab></md-tabs></md-content></span><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\" ng-show=atoTermo.tipoModeloContrato><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=fechar()><md-tooltip md-direction=bottom>{{\'tooltip-fechar\' | translate}}</md-tooltip>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised type=submit value=PRE_VISUALIZAR ng-click=\"acaoSubmit=PRE_VISUALIZAR\"><md-tooltip md-direction=bottom>{{\'tooltip-pre-visualizar\' | translate}}</md-tooltip>{{\'btn-pre-visualizacao\' | translate}}</md-button></section></div><!--- fim dos botoes --></md-content></form></md-content>");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/consultarAtosContratosTermo.html","<md-content flex><titulo-servico ng-if=menuAcessos></titulo-servico><form name=dataTableImovel><md-content flex><div layout=row><md-input-container flex=25><label>{{\'nome-ato-termo\' | translate}}</label><input ng-model=filtro.descricao></md-input-container><md-input-container flex=25><label>{{\'label-modulo\' | translate}}</label><md-select ng-model=filtro.modulo ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=buscarFuncionalidadePorModulo() name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in modulos | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=25><label>{{\'label-tipo\' | translate}}</label><md-select ng-model=filtro.tipoModeloContrato ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-tipo\' | translate}}\" ng-change=selecionarTipo()><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in tipoModelo | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=25><label>{{\'label-funcionalidade\' | translate}}</label><md-select ng-model=filtro.instrumentoFuncionalidade ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-funcionalidade\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in funcionalidades | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=limparCamposPesquisa()><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{LIMPAR}}</md-button><md-button class=md-raised ng-click=buscarDadosModelosPaginacao()><md-tooltip md-direction=bottom>{{\'tooltip-pesquiser-modelo\' | translate}}</md-tooltip>{{PESQUISAR}}</md-button><md-button class=md-raised ng-click=\"redirecionarNovoAtoContrato(); acaoSubmit=NOVO\"><md-tooltip md-direction=bottom>{{\'tooltip-novo-modelo\' | translate}}</md-tooltip>{{NOVO}}</md-button></section></div><!--- fim dos botoes --></md-content></form><div flex style=\"margin-top: 3%\"><div layout=column><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro>{{\'nome-ato-termo\' | translate}}</th><th md-column class=texto-centro>{{\'label-modulo\' | translate}}</th><th md-column class=texto-centro>{{\'label-tipo\' | translate}}</th><th md-column class=texto-centro>{{\'label-funcionalidade\' | translate}}</th><th md-column class=texto-centro>{{\'label-despacho\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"atosContratos in listaAtosContratos\"><td md-cell class=texto-centro-corpo><span ng-bind=atosContratos.descricao></span></td><td md-cell class=texto-centro-corpo><span ng-bind=atosContratos.modulo.descricao></span></td><td md-cell class=texto-centro-corpo><span ng-bind=atosContratos.tipoModeloContrato.descricao ng-click=\"acaoSubmit=EDITAR\"></span></td><td md-cell class=texto-centro-corpo><span ng-bind=atosContratos.instrumentoFuncionalidade.descricao ng-click=\"acaoSubmit=EDITAR\"></span></td><td md-cell class=texto-centro-corpo><span ng-bind=atosContratos.despacho.descricao></span></td><td md-cell class=texto-centro-corpo><div layout=row layout-align=\"center center\"><div layout-margin=5><a ng-href=\"#/integrador/alterarContratoTermo?idContrato={{atosContratos.id}}\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"{{\'label-editar-modelo\' | translate}} \"><md-tooltip>{{\'label-editar-modelo\' | translate}}</md-tooltip></ng-md-icon></a></div><div layout-margin=5><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-modelo\' | translate}}\" ng-click=\"modalConfirmarExclusaoAtos($event, atosContratos.id);\"><md-tooltip>{{\'label-excluir-modelo\' | translate}}</md-tooltip></ng-md-icon></div></div></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=buscarDadosModelosPaginacao md-page-select></md-table-pagination></md-conent></div></div></md-content>");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/modeloCertidao.html","<div flex=70 layout-align=center style=\"margin-left: 15%\"><div layout=row layout-align=\"center center\" class=brasao ng-init=\"brasao = \'assets/img/brasao.jpg\'\"><img ng-src={{brasao}}></div><h4 class=titulo-ministerio href=# editable-text=atoTermo.configuracaoAtosContratos[0].valorLabel>{{atoTermo.configuracaoAtosContratos[0].valorLabel || \'empty\'}} <i class=material-icons>mode_edit</i></h4><!--h4 class=\"titulo-ministerio\">{{\'label-secretaria-uniao\' | translate}}</h4--><h3 class=titulo>{{atoTermo.descricao}} / {{formmatarCodigoGerado(codigoGerado)}}</h3><div><md-content flex><md-table-container><table md-table><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"componentes in dictConfigurarComponente\"><td md-cell class=\"celula tamanho-setas\" style=\"border-top: 0% !important; height: 0% !important\"><div layout=column flex=10><i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index-1, dictConfigurarComponente)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index+1, dictConfigurarComponente)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div ng-if=\"item.hierarquia == \'PRIMARIO\'\" ng-repeat=\"item in componentes.item\"><div layout=row class=titulo-componente style=\"height: 30px\"><div ng-hide=!item.status><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox></div><h2 href=# editable-text=item.descricao ng-hide=\"item.identificador == \'COMPETENCIA_ASSINATURA\'\" style=\"margin-top: 5px\">{{item.descricao}} <i class=material-icons>mode_edit</i></h2><h2 ng-hide=\"item.identificador != \'COMPETENCIA_ASSINATURA\'\" style=\"margin-top: 5px; color: black\">{{item.descricao}}</h2></div></div><div class=\"alinhar-checkbox secundario\" ng-if=\"item.hierarquia == \'SECUNDARIO\'\" ng-repeat=\"item in componentes.item\" style=\"height: 20px !important\" layout=column><div layout=row><div ng-hide=\"item.identificador == \'VALOR_CONTEUDO\'\"><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox></div><h4 style=\"margin-top: 4px;margin-left: 5px\" ng-hide=\"item.identificador == \'VALOR_CONTEUDO\'\"><div href=# editable-text=item.descricao ng-bind-html=item.descricao><i class=material-icons>mode_edit</i></div></h4><h4 class=valor-conteudo ng-hide=\"item.identificador != \'VALOR_CONTEUDO\'\"><div ng-bind-html=item.descricao></div></h4><h4 href=# editable-text=item.label style=\"font-weight: normal !important;\n                                    margin-top: 5px\" ng-class=\"{espacoLabel: item.descricao}\">{{item.label}} <i class=material-icons>mode_edit</i></h4></div><div layout=row><div class=\"alinhar-checkbox terciario\" ng-if=\"itemTerciario.hierarquia == \'TERCIARIO\'\" ng-repeat=\"itemTerciario in item.item\" style=\"height: 20px\" layout=row><md-checkbox ng-checked=itemTerciario.status class=md-primary ng-click=\"itemTerciario.status = !itemTerciario.status\"></md-checkbox><h4 href=# editable-text=itemTerciario.descricao style=\"margin-top: 4px\">{{itemTerciario.descricao}}: <i class=material-icons>mode_edit</i></h4></div></div></div></td></tr></tbody></table></md-table-container><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\" ng-show=atoTermo.tipoModeloContrato><md-button class=md-raised ng-click=voltar()><md-tooltip md-direction=bottom>{{\'tooltip-voltar\' | translate}}</md-tooltip>{{\'btn-voltar\' | translate}}</md-button><md-button class=md-raised name=salvar ng-click=salvar()><md-tooltip md-direction=bottom>{{\'tooltip-salvar\' | translate}}</md-tooltip>{{\'btn-salvar\' | translate | uppercase}}</md-button></div><!--- fim dos botoes --></md-content></div></div>");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/modeloContrato.html","<div flex=70 layout-align=center style=\"margin-left: 15%\"><div layout=row layout-align=\"center center\" class=brasao ng-init=\"brasao = \'assets/img/brasao.jpg\'\"><img ng-src={{brasao}}></div><h4 class=titulo-ministerio href=# editable-text=tituloMinisterio>{{tituloMinisterio}} <i class=material-icons>mode_edit</i></h4><!--h4 class=\"titulo-ministerio\">{{\'label-secretaria-uniao\' | translate}}</h4--><h3 class=titulo>{{\'label-contrato-termo\' | translate | uppercase}} / {{formmatarCodigoGerado(codigoGerado)}}</h3><div><div layout=row><div flex=60></div><div flex=40><h4 style=\"text-align:justify; text-justify: inter-word\">{{atoTermo.descricao}}</h4></div></div><md-content flex class=modelo-contrato><md-content style=\"padding-left: 0px !important\"><label ng-bind-html=atoTermo.preambulo.descricao></label><div layout=row class=fundamento-legal><label>{{\'fundamento-legal\' | translate}}:</label>{{removerHTML(atoTermo.fundamentoLegal.descricao)}}</div><div layout=row class=codigo-atendimento><label>{{\'codigo-atendimento\' | translate}}:</label>[Código do Atendimento]/[Nº do Processo]</div></md-content><md-table-container><table md-table><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"componentes in dictConfigurarComponente\"><td md-cell class=\"celula tamanho-setas\" style=\"border-top: 0% !important; height: 0% !important\"><div layout=column flex=10><i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index-1, dictConfigurarComponente)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index+1, dictConfigurarComponente)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div ng-if=\"item.hierarquia == \'PRIMARIO\'\" ng-repeat=\"item in componentes.item\"><div layout=row class=titulo-componente style=\"height: 30px\"><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox><h2 href=# editable-text=item.descricao style=\"margin-top: 5px\" ng-class=\"{grey: !item.status}\">{{item.descricao}} <i class=material-icons>mode_edit</i></h2></div><h4 href=# editable-text=item.label class=componentes-complemento>{{item.label}} <i class=material-icons>mode_edit</i></h4></div><div class=alinhar-checkbox ng-if=\"item.hierarquia == \'SECUNDARIO\'\" ng-repeat=\"item in componentes.item\" layout=column><div layout=column><div layout=row><div ng-hide=\"item.descricao == \'CLAUSULAS\'\"><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox></div><h4 href=# editable-text=item.descricao ng-hide=\"item.descricao == \'CLAUSULAS\'\" ng-class=\"{grey: !item.status}\" class=secundario-titulo>{{item.descricao}}: <i class=material-icons>mode_edit</i></h4><h4 href=# editable-text=item.label ng-hide=\"item.descricao == \'CLAUSULAS\'\" ng-class=\"{grey: !item.status}\" class=secundario-label>{{item.label}} <i class=material-icons>mode_edit</i></h4><h4 ng-hide=\"item.descricao != \'CLAUSULAS\'\" class=clausulas>{{item.identificador}}:</h4><h4 ng-hide=\"item.descricao != \'CLAUSULAS\'\" class=clausulas-label>{{item.label}}</h4></div><div class=\"alinhar-checkbox terciario\" ng-if=\"itemTerciario.hierarquia == \'TERCIARIO\'\" ng-repeat=\"itemTerciario in item.item\" layout=row><md-checkbox ng-checked=itemTerciario.status class=md-primary ng-click=\"itemTerciario.status = !itemTerciario.status\"></md-checkbox><h4 ng-class=\"{grey: !itemTerciario.status}\" class=terciario-label>{{itemTerciario.descricao}}:</h4></div></div></div></td></tr></tbody></table></md-table-container><br><br><br><br>{{getLabel(\'dataGerada\', atoTermo.configuracaoAtosContratos)}}<br><br><br><br><md-content style=\"text-align: -webkit-center\"><div class=assinaturas layout=row layout-align=\"center center\" flex=20>{{atoTermo.denominacaoPartes.uniao}}</div><div layout=row layout-align=\"center center\"><label>UNIÃO-[nome do representante(de quem assina)]</label></div></md-content><md-content style=\"text-align: -webkit-center\"><div class=assinaturas layout=row layout-align=\"center center\" flex=20>{{atoTermo.denominacaoPartes.terceiro}}</div><div layout=row layout-align=\"center center\"><label>[nome do representante(do CNPJ)]</label></div></md-content><md-content style=\"text-align: -webkit-center\"><div class=assinaturas layout=row layout-align=\"center center\" flex=20>{{atoTermo.denominacaoPartes.terceiro}}</div><div layout=row layout-align=\"center center\"><label>[nome da Pessoa 1)]</label></div></md-content><md-content style=\"text-align: -webkit-center\"><div class=assinaturas layout=row layout-align=\"center center\" flex=20>{{atoTermo.denominacaoPartes.terceiro}}</div><div layout=row layout-align=\"center center\"><label>[nome do conjuge da Pessoa(se houver)]</label></div></md-content><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\" ng-show=atoTermo.tipoModeloContrato><md-button class=md-raised ng-click=voltar()><md-tooltip md-direction=bottom>{{\'tooltip-voltar\' | translate}}</md-tooltip>{{\'btn-voltar\' | translate}}</md-button><md-button class=md-raised name=salvar ng-click=salvar()><md-tooltip md-direction=bottom>{{\'tooltip-salvar\' | translate}}</md-tooltip>{{\'btn-salvar\' | translate | uppercase}}</md-button></div><!--- fim dos botoes --></md-content></div></div>");
$templateCache.put("scripts-integrador/pages/consulta-orientacao-tecnica/language/pt-BR.json","{ \"label-titulo\": \"Título\", \"label-area\": \"Área\", \"label-vigente\": \"Vigente\", \"label-assunto\": \"Assunto\", \"button-editar\": \"Editar\", \"button-deletar\": \"Deletar\", \"button-visualizar\": \"Visualizar\" }");
$templateCache.put("scripts-integrador/pages/consulta-orientacao-tecnica/view/consultaOrientacaoTecnica.html","<titulo-servico class=texto-principal></titulo-servico><form name=dataTableImovel><md-content flex><div layout=row><md-input-container flex><label>{{\'label-titulo\' | translate}}</label><input ng-model=filtro.titulo></md-input-container></div><div layout=row><md-input-container flex><label>{{\'label-area\' | translate}}</label><md-select ng-model=filtro.listAreas multiple md-on-close=clearSearchArea()><md-select-header class=integradorSelectSearch><input ng-model=searchArea type=search placeholder=\"Pesquisar Área\" class=\"demo-header-searchbox md-text\"></md-select-header><md-optgroup><md-option ng-value=area.descricao ng-repeat=\"area in areasOrientacaoTecnica | filter:searchArea\">{{area.descricao}}</md-option></md-optgroup></md-select></md-input-container><md-input-container flex><label>{{\'label-vigente\' | translate}}</label><md-select ng-model=filtro.vigente><md-option ng-value=true>Ativo</md-option><md-option ng-value=false>Suspenso</md-option></md-select></md-input-container></div><div layout=row><md-input-container flex=50><label>{{\'label-assunto\' | translate}}</label><md-select ng-model=filtro.listAssuntos multiple md-on-close=clearSearchAssunto()><md-select-header class=integradorSelectSearch><input ng-model=searchAssunto type=search placeholder=\"Pesquisar Assunto\" class=\"demo-header-searchbox md-text\"></md-select-header><md-optgroup><md-option ng-value=assunto.descricao ng-repeat=\"assunto in assuntosOrientacaoTecnica | filter:searchAssunto\">{{assunto.descricao}}</md-option></md-optgroup></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=clearFilter()><md-tooltip md-direction=bottom>{{\'toltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate }}</md-button><md-button class=md-raised ng-click=pesquisar()><md-tooltip md-direction=bottom>{{\'toltip-pesquiser-servicos\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate }}</md-button><md-button class=md-raised ng-click=novoCadastro()><md-tooltip md-direction=bottom>{{\'toltip-adicionar-servicos\' | translate}}</md-tooltip>{{\'btn-novo\' | translate }}</md-button></section></div><!--- fim dos botoes --></md-content></form><div flex style=\"margin-top: 3%\" class=custom-style><div layout=column><md-conent flex><md-table-container><table md-table ng-model=vm.imovelSelecionado><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro>Título</th><th md-column class=texto-centro>Área</th><th md-column class=texto-centro style=\"text-align: center !important\">Assunto</th><th md-column class=texto-centro style=\"text-align: center !important\">Publicar</th><th md-column class=texto-centro style=\"text-align: center !important\">Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaOrientacaoTecnica\"><td md-cell class=texto-centro-corpo><span ng-bind=item.titulo></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.area></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.assunto></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.publicado></span></td><td md-cell class=texto-centro-corpo style=\"display: block; position: relative\"><md-fab-speed-dial md-direction=left style=\"margin-right: 50%; margin-top: -3px; transform: translateX(20%)\" class=\"hand md-scale alinhar-md-fab-speed-dial\" layout-align=\"end end\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><a href=#/integrador/orientacaoTecnica/editar/{{item.id}} ng-click=editarPop($index) ng-if=!consultar><ng-md-icon class=icon-subacao icon=create style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-editar\' | translate}}</md-tooltip></ng-md-icon></a><a href=#/integrador/orientacaoTecnica/consultar/{{item.id}}><ng-md-icon class=icon-subacao icon=search style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-visualizar\' | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto; background-color: #5bf869\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findPagination md-page-select></md-table-pagination></md-conent></div></div>");
$templateCache.put("scripts-integrador/pages/documento/language/pt-BR.json","{ \"label-nome-documento\":\"Nome do Documento\", \"label-extensao\":\"Extensões do documento\", \"label-pessoa-fisica\":\" Pessoa Fisíca\", \"label-pessoa-juridica\":\"Pessoa Jurídica\", \"label-imovel\":\"Imóvel\", \"label-adicionar-nova-extensao\":\"Adicionar Nova Extensão\", \"label-situacao\":\"Situação\", \"button-fechar\":\"Fechar\", \"button-limpar\":\"Limpar\", \"button-salvar\":\"Salvar\", \"button-novo\": \"Novo\", \"button-editar\": \"Editar\", \"button-pesquisar\": \"Pesquisar\", \"label-vinculo\":\"Vínculo\", \"title-consultar-documentos\": \"Consultar Documentos\" }");
$templateCache.put("scripts-integrador/pages/documento/view/cadastroDocumento.html","<md-content xmlns=http://www.w3.org/1999/html><h2 class=tituloFormulario>Documentos</h2><ng-form name=formCadastroDocumentos><md-dialog-content><md-input-container class=md-block flex-gt-xs flex=100><label>{{\'label-nome-documento\' |translate}}</label><input ng-model=documento.nome type=text name=nome required md-maxlength=1000 maxlength=1000></md-input-container></md-dialog-content><div><div style=\"margin-bottom: 10px\"><label>{{\'label-extensao\'| translate}}</label></div><div layout=row flex-offset=0 layout-aling=\"center center\"><div flex=10 ng-repeat=\"formatoArq in listaFormatosArquivos\"><md-checkbox class=md-primary ng-model=formatoArq.selecionado>{{ \'.\' + (formatoArq.formato | lowercase) }}</md-checkbox></div><div><md-checkbox class=md-primary ng-model=statusExtensaoDocumento>{{ \'Outro\' }}</md-checkbox></div></div></div><md-dialog-content ng-if=statusExtensaoDocumento><md-input-container class=md-block flex-gt-xs flex=100><label>{{\'label-adicionar-nova-extensao\' |translate}}</label><input ng-model=documento.outro ng-required=statusExtensaoDocumento type=text name=nome></md-input-container></md-dialog-content><md-dialog-content><div style=\"margin-bottom: 10px\"><label>{{\'label-vinculo\'| translate}}</label></div><div layout=row flex-offset=0 class=margin-top layout-wrap layout-aling=\"end start\"><div flex=15><md-checkbox class=md-primary ng-disabled=isEditar ng-model=documento.pessoaFisica><label>{{\'label-pessoa-fisica\' |translate}}</label></md-checkbox></div><div flex=15><md-checkbox class=md-primary ng-disabled=isEditar ng-model=documento.pessoaJuridica><label>{{\'label-pessoa-juridica\' |translate}}</label></md-checkbox></div><div flex=15><md-checkbox class=md-primary ng-disabled=isEditar ng-model=documento.imovel><label>{{\'label-imovel\' |translate}}</label></md-checkbox></div><md-input-container style=\"margin-top: 0px\" flex=50 class=md-block><label>{{\'label-situacao\'| translate}}</label><md-select required ng-model=documento.situacao><md-option ng-value=true>Ativo</md-option><md-option ng-value=false>Inativo</md-option></md-select></md-input-container></div></md-dialog-content><div style=margin-top:150px><section layout=row layout-sm=column layout-align=\"end center\" layout-wrap><md-button class=md-raised ng-click=fechar()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=md-raised ng-click=limparCampos()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=md-raised ng-click=validarFormulario()>{{\'button-salvar\' | translate}}</md-button></section></div></ng-form><md-content></md-content></md-content>");
$templateCache.put("scripts-integrador/pages/documento/view/consultaDocumentosPessoa.html","<md-content><h2 class=tituloFormulario>{{\'title-consultar-documentos\' | translate}}</h2><md-dialog-content><md-input-container class=md-block flex-gt-xs flex=100><label>{{\'label-nome-documento\' |translate}}</label><input ng-model=filtroConsulta.nome type=text name=nome md-maxlength=1000 maxlength=1000></md-input-container></md-dialog-content><md-dialog-content><label>{{\'label-vinculo\'| translate}}</label><div layout=row flex-offset=0 class=margin-top layout-wrap layout-aling=\"end start\"><div flex=20><md-checkbox class=md-primary ng-model=filtroConsulta.pessoaFisica><label>{{\'label-pessoa-fisica\' |translate}}</label></md-checkbox></div><div flex=20><md-checkbox class=md-primary ng-model=filtroConsulta.pessoaJuridica><label>{{\'label-pessoa-juridica\' |translate}}</label></md-checkbox></div><div flex=20><md-checkbox class=md-primary ng-model=filtroConsulta.imovel><label>{{\'label-imovel\' |translate}}</label></md-checkbox></div><md-input-container style=\"margin-top: 0px\" flex=40 class=md-block><label>{{\'label-situacao\'| translate}}</label><md-select ng-model=filtroConsulta.situacao><md-option ng-value=true>Ativo</md-option><md-option ng-value=false>Inativo</md-option></md-select></md-input-container></div></md-dialog-content><div style=margin-bottom:60px layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=redirecionarCadastrarDocumentosPessoa()><label>{{\'button-novo\' | translate}}</label></md-button><md-button class=md-raised ng-click=limparConsulta()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=md-raised ng-click=consultarDocumentosPessoa()><label>{{\'button-pesquisar\' | translate}}</label></md-button></section></div><md-content flex ng-show=\"paginadosDocumentosPessoa.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-nome\' | translate}}</th><th md-column>{{\'label-extensoes\' | translate}}</th><th md-column>{{\'label-vinculo\' | translate}}</th><th md-column>{{\'label-situacao\' | translate}}</th><th md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documentoPessoa in paginadosDocumentosPessoa\"><td md-cell><label ng-bind=documentoPessoa.nome></label></td><td md-cell><label ng-bind=exibirFormatosArquivoDocumento(documentoPessoa)></label><label ng-bind=exibirOutrosFormatos(documentoPessoa)></label></td><td md-cell><label ng-bind=documentoPessoa.vinculo></label></td><td md-cell><label ng-bind=validarSituacaoDocumento(documentoPessoa.situacao)></label></td><td md-cell><md-fab-speed-dial md-direction=left style=\"margin-right: 9%; margin-top: -14px\" class=\"hand md-scale alinhar-md-fab-speed-dial\" layout-align=\"end end\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><a href=\"\" ng-click=redirecionarEditarDocumentoPessoa(documentoPessoa.id)><ng-md-icon class=icon-subacao icon=create style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-editar\' | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginarDocumentosPessoa md-page-select></md-table-pagination></md-content></md-content>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/language/pt-BR.json","{ \"label-cadastrar-fundamento-legal\": \"Cadastrar Fundamento Legal\", \"label-consultar-fundamento-legal\": \"Consultar Fundamento Legal\", \"label-instrumento-funcionalidade\": \"Instrumento/Funcionalidade\", \"label-fundamento-legal\": \"Fundamento Legal\", \"label-descricao-fundamento-legal\": \"Descrição do Fundamento Legal\", \"label-status\": \"Status\", \"label-todos\": \"Todos\", \"label-cancelar-fundamento-legal\": \"Cancelar Fundamento Legal\", \"label-editar-fundamento-legal\":\"Editar Fundamento Legal\", \"msg-dado-cancelado-com-sucesso\": \"Dado cancelado com sucesso\", \"label-configuracoes\": \"Configurações\", \"label-utilizar-mesmas-configuracoes-fundamento\": \"Utilizar as mesmas configurações do Fundamento\", \"label-competencia\": \"Competência\", \"label-exigir-ato-autorizativo\": \"Exigir Ato Autorizativo\", \"label-superintendente\": \"Superintendente\", \"label-secretario\": \"Secretário\", \"label-ministro\": \"Ministro\", \"label-criterio-a-ser-utilizado\": \"Critério a ser utilizado\", \"label-apresenta-diferenca-por-regiao\": \"Apresenta diferença por região?\", \"label-apresenta-diferenca-por-natureza-urbano-rural\": \"Apresenta diferença por natureza (urbano/rural)?\", \"label-regioes\": \"Regiões\", \"label-natureza\": \"Natureza\", \"label-area-ha\": \"área (ha)\", \"label-valor-rs\": \"valor (R$)\", \"label-ou\": \"ou\", \"label-minimo\": \"Mínimo\", \"label-maximo\": \"Máximo\", \"label-todas\": \"Todas\", \"label-urbano\": \"Urbano\", \"label-rural\": \"Rural\", \"label-norte\": \"Norte\", \"label-nordeste\": \"Nordeste\", \"label-centro-oeste\": \"Centro Oeste\", \"label-sudeste\": \"Sudeste\", \"label-sul\": \"Sul\", \"btn-fechar\": \"Fechar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-novo\": \"Novo\", \"btn-editar\": \"Editar\", \"label-utilizacao\": \"Utilização\", \"label-tipo-uso-permitido\": \"Tipo de Uso Permitido\", \"label-especificacao\": \"Especificação\", \"label-adicionar\": \"Adicionar\", \"label-acao\": \"Ação\", \"label-responsavel-destinatario\": \"Responsável/Destinatário\", \"label-natureza-juridica-permitida\": \"Natureza Jurídica permitida\", \"label-inclur-area-fracao-utilizada-por-responsavel\": \"Incluir Área/Fração Utilizada por Responsável?\", \"label-permitir-imovel-ja-parcelado\":\"Permitir Imóvel já parcelado?\", \"label-sim\": \"Sim\", \"label-nao\": \"Não\", \"label-ativo\":\"Ativo\", \"label-inativo\":\"Inativo\", \"label-objeto-imovel-parcela\": \"Objeto (Imóvel/Parcela)\", \"label-permitir-imoveis-tipo-espelho-d-agua\": \"Permitir imóveis do tipo \\\"Espelho D\'água\\\"\", \"label-permitir-imoveis-tipo-cavidade-natural-subterranea\": \"Permitir imóveis do tipo \\\"Cavidade Natural Subterrânea\\\"\", \"label-natureza-imovel-permitido\": \"Natureza do Imóvel permitido\", \"label-situacao-incorporacao-imovel-permitido\": \"Situação da Incorporação do Imóvel permitido\", \"label-em-progresso-incorporacao\": \"Em processo de incorporação\", \"label-incorporado\": \"Incorporado\", \"label-nao-se-aplica\": \"Não se aplica\", \"label-forma-incorporacao-permitida\": \"Forma de Incorporação permitida\", \"label-tipo-aquisicao-permitida\": \"Tipo de Aquisição permitida\", \"label-entidade-extinta\": \"Entidade Extinta\", \"label-excluir\": \"Excluir\", \"label-avaliacao-imovel\": \"Avaliação do Imóvel\", \"label-necessario-avaliacao-imovel\": \"Necessário avaliação do imóvel\", \"label-tipo-avaliacao-necessaria\": \"Tipo de avaliação necessário\", \"label-avaliacao-terreno-referencia-pvg-spu\": \"Avaliação de Terreno - referência PVG/SPU\", \"label-avaliacao-terreno-referencia-valor-venal-municipio\": \"Avaliação de Terreno - referência Valor Venal/Município\", \"label-avaliacao-terreno-rural-referencia-incra\": \"Avaliação de Terreno Rural - referência INCRA\", \"btn-salvar\": \"Salvar\", \"label-detalhar\": \"Detalhar Fundamento Legal\", \"label-definicao-metodologia-avaliacao\": \"Definição da metodologia de Avaliação\", \"label-tipo-avaliacao\": \"Tipo de Avaliação\", \"label-definir-percentual-valor\": \"Definir percentual do valor\", \"label-valor-percentual\": \"Percentual do valor\", \"label-ordem-prioridade\": \"Ordem de Prioridade\", \"label-maior-valor\": \"Maior Valor\", \"btn-adicionar\": \"Adicionar\", \"label-tabela-ordem\": \"Ordem\", \"label-tabela-avaliacao\": \"Avaliação\", \"label-tabela-acoes\": \"Ações\", \"label-excluir-tipo-avaliacao\": \"Excluir\", \"erro-percentual-invalido\": \"Percentual inválido\" }");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/cadastrarFundamentoLegal.html","<ng-form name=formularioDadosFundamentoLegal novalidate><md-content flex><div id=formData layout=column><toaster-container></toaster-container><div layout=row flex><h2 class=tituloFormulario>{{isEditar ? \'label-editar-fundamento-legal\' : isDetalhar ? \'label-detalhar\' : \'label-cadastrar-fundamento-legal\' | translate}}</h2></div><div ng-if=\"!fundamentoLegal.ativo && fundamentoLegal.id\"><fieldset style=\"border-radius: 10px; padding: 2%; border-color: #80808040\"><p class=tituloFormulario>Detalhar Cancelamento do Fundamento Legal</p><div><b><span>{{\'label-responsavel\' | translate}}:</span></b> <span>{{dadosCancelamento.responsavel}}</span></div><div style=\"margin:1% 0 1% 0\"><b><span>{{\'label-data\' | translate}}:</span></b> <span>{{dadosCancelamento.dataSuspensao | date: \'dd/MM/yyyy\'}}</span></div><div style=\"margin-top: 2%\"><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=dadosCancelamento.justificativa name=\"{{\'label-justificativa\' | translate}}\" maxlength=500 md-maxlength=500 disabled></textarea></md-input-container></div></fieldset></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=100 class=md-block><label>{{\'label-instrumento-funcionalidade\'| translate}}</label><md-select ng-model=fundamentoLegal.idInstrumentoFuncionalidade required ng-disabled=\"isEditar || isDetalhar\"><md-option ng-repeat=\"instrumento in listaInstrumentosFuncionalidade\" ng-value=instrumento.instrumentoFuncionalidade.id>{{instrumento.instrumentoFuncionalidade.modulo.descricao + \' / \' + instrumento.instrumentoFuncionalidade.descricao + \' / \' + instrumento.titulo}}</md-option></md-select></md-input-container></div><label>{{\'label-fundamento-legal\' | translate}}</label><div layout=row><md-input-container flex=100 class=textangular><div text-angular required ng-disabled=true contenteditable=false ng-model=fundamentoLegal.template name=descricao ta-max-text=2000 ta-toolbar-button-class=\"btn btn-default\" ta-toolbar-button-active-class=active name=\"{{\'label-fundamento-legal\' | translate}}\"></div></md-input-container></div><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><label>{{\'label-descricao-fundamento-legal\' | translate}}:</label><textarea ng-disabled=isDetalhar ng-model=fundamentoLegal.descricao ui-mask-placeholder-char=space required name=\"{{\'label-descricao-fundamento-legal\' | translate}}\" md-maxlength=500 maxlength=500></textarea></md-input-container></div><div layout=row flex><h3>{{\'label-configuracoes\' | translate}}</h3></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=100 class=md-block><label>{{\'label-utilizar-mesmas-configuracoes-fundamento\'| translate}}</label><md-select ng-disabled=isDetalhar ng-model=fundamentoLegal.utilizarFundamentoLegal ng-model-options=\"{trackBy:\'$value.id\'}\" ng-change=utilizarFundamento()><md-option ng-value=\"\"></md-option><md-option ng-repeat=\"fundamentoCadastrado in listaFundamentosCadastrados\" ng-value=fundamentoCadastrado>{{fundamentoCadastrado.descricao}}</md-option></md-select></md-input-container></div><div layout=column layout-xs=column layout-sm=column flex=100><label style=\"margin-bottom: 1%\">{{\'label-competencia\'| translate}}</label><md-checkbox class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isExigirAtoAutorizativo name=\"{{\'label-exigir-ato-autorizativo\' | translate}}\" flex=25 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-exigir-ato-autorizativo\' | translate}}</label></md-checkbox><div layout=column layout-xs=column layout-sm=column flex=100 flex-offset=5 ng-if=fundamentoLegal.isExigirAtoAutorizativo><md-checkbox ng-required=\"!fundamentoLegal.isSecretario && !fundamentoLegal.isMinistro\" class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-superintendente\' | translate}}</label></md-checkbox><md-checkbox ng-required=\"!fundamentoLegal.isSuperintendente && !fundamentoLegal.isMinistro\" class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-secretario\' | translate}}</label></md-checkbox><md-checkbox ng-required=\"!fundamentoLegal.isSuperintendente && !fundamentoLegal.isSecretario\" class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-ministro\' | translate}}</label></md-checkbox></div></div><div ng-show=validarCriterioUtilizado() layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=33 class=md-block><label>{{\'label-criterio-a-ser-utilizado\'| translate}}</label><md-select ng-model=fundamentoLegal.criterio ng-required=validarCriterioUtilizado() ng-disabled=\"bloquear || isDetalhar\"><md-option ng-repeat=\"criterio in listaCriterios\" ng-value=criterio.nome>{{criterio.descricao}}</md-option></md-select></md-input-container><md-input-container style=\"margin-top: 0px\" flex=33 class=md-block><label>{{\'label-apresenta-diferenca-por-regiao\'| translate}}</label><md-select ng-model=fundamentoLegal.isDiferencaRegiao ng-disabled=\"bloquear || isDetalhar\" ng-required=validarCriterioUtilizado()><md-option ng-value=true>{{\'label-sim\' | translate}}</md-option><md-option ng-value=false>{{\'label-nao\' | translate}}</md-option></md-select></md-input-container><md-input-container style=\"margin-top: 0px\" flex=33 class=md-block><label>{{\'label-apresenta-diferenca-por-natureza-urbano-rural\'| translate}}</label><md-select ng-model=fundamentoLegal.isDiferencaNatureza ng-disabled=\"bloquear || isDetalhar\" ng-required=validarCriterioUtilizado()><md-option ng-value=true>{{\'label-sim\' | translate}}</md-option><md-option ng-value=false>{{\'label-nao\' | translate}}</md-option></md-select></md-input-container></div><div ng-if=\"validarCriterioUtilizado() && fundamentoLegal.criterio.length > 0 && fundamentoLegal.isDiferencaRegiao==configuracoesTodasTodas[0].isDiferencaRegiao && fundamentoLegal.isDiferencaNatureza==configuracoesTodasTodas[0].isDiferencaNatureza \" ng-include=\"\'scripts-integrador/pages/fundamento-legal/view/partial/configuracoesTodasTodas.html\'\"></div><div ng-if=\"validarCriterioUtilizado() && fundamentoLegal.criterio.length > 0 && fundamentoLegal.isDiferencaRegiao==configuracoesTodasNatureza[0].isDiferencaRegiao && fundamentoLegal.isDiferencaNatureza==configuracoesTodasNatureza[0].isDiferencaNatureza\" ng-include=\"\'scripts-integrador/pages/fundamento-legal/view/partial/configuracoesTodasNatureza.html\'\"></div><div ng-if=\"validarCriterioUtilizado() && fundamentoLegal.criterio.length > 0 && fundamentoLegal.isDiferencaRegiao==configuracoesRegiaoNatureza[0].isDiferencaRegiao && fundamentoLegal.isDiferencaNatureza==configuracoesRegiaoNatureza[0].isDiferencaNatureza\" ng-include=\"\'scripts-integrador/pages/fundamento-legal/view/partial/configuracoesRegiaoNatureza.html\'\"></div><div ng-if=\"validarCriterioUtilizado() && fundamentoLegal.criterio.length > 0 && fundamentoLegal.isDiferencaRegiao==configuracoesRegiaoTodas[0].isDiferencaRegiao && fundamentoLegal.isDiferencaNatureza==configuracoesRegiaoTodas[0].isDiferencaNatureza\" ng-include=\"\'scripts-integrador/pages/fundamento-legal/view/partial/configuracoesRegiaoTodas.html\'\"></div><incluir-utilizacao utilizacoes=fundamentoLegal.utilizacoes bloquear=bloquear is-detalhar=isDetalhar></incluir-utilizacao><br><div layout=column ng-if=\"moduloSelecionado==DESTINACAO\"><div layout=row flex><h3>{{\'label-responsavel-destinatario\' | translate}}</h3></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=50 class=md-block><label>{{\'label-natureza-juridica-permitida\'| translate}}</label><md-select ng-model=fundamentoLegal.naturezaJuridicaPermitidas ng-model-options=\"{trackBy:\'$value.descricao\'}\" multiple ng-disabled=\"bloquear || isDetalhar\" required><md-input-container style=\"margin: 0\"><md-checkbox ng-model=todosResponsaveis class=md-primary ng-change=selecionarTodosResponsavel(todosResponsaveis)>{{\'label-todos\' | translate}}</md-checkbox></md-input-container><md-option ng-repeat=\"natureza in listaNaturezasPermitidas\" ng-value=natureza>{{natureza.descricao}}</md-option></md-select></md-input-container></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input class=md-block flex layout=column><label>{{\'label-inclur-area-fracao-utilizada-por-responsavel\' | translate}}:</label><md-radio-group style=\"margin: 2% 0 0 2%\" class=md-block required ng-model=fundamentoLegal.isIncluirAreaFracaoResponsavel layout=row name=\"{{\'label-inclur-area-fracao-utilizada-por-responsavel\' | translate}}\" ng-disabled=\"bloquear || isDetalhar\"><md-radio-button class=md-primary ng-value=true><label>{{\'label-sim\' | translate}}</label></md-radio-button><md-radio-button class=md-primary ng-value=false><label>{{\'label-nao\' | translate}}</label></md-radio-button></md-radio-group></md-input></div><br><div layout=row flex><h3>{{\'label-objeto-imovel-parcela\' | translate}}</h3></div><div layout=column layout-xs=column layout-sm=column flex=100><md-checkbox class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isPermitirImoveisEspelhoDAgua name=\"{{\'label-permitir-imoveis-tipo-espelho-d-agua\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-permitir-imoveis-tipo-espelho-d-agua\' | translate}}</label></md-checkbox><md-checkbox class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isPermitirImoveisCavidadeNatural name=\"{{\'label-permitir-imoveis-tipo-cavidade-natural-subterranea\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-permitir-imoveis-tipo-cavidade-natural-subterranea\' | translate}}</label></md-checkbox></div><div layout=row flex-offset=0 class=margin-top style=\"margin-bottom: 2%\" layout-wrap><md-input class=md-block flex layout=column><label>{{\'label-permitir-imovel-ja-parcelado\' | translate}}:</label><md-radio-group style=\"margin: 2% 0 0 2%\" class=md-block required ng-model=fundamentoLegal.isPermitirImovelParcelado layout=row name=\"{{\'label-permitir-imovel-ja-parcelado\' | translate}}\" ng-disabled=\"bloquear || isDetalhar\"><md-radio-button class=md-primary ng-value=true><label>{{\'label-sim\' | translate}}</label></md-radio-button><md-radio-button class=md-primary ng-value=false><label>{{\'label-nao\' | translate}}</label></md-radio-button></md-radio-group></md-input></div><br><div layout=row layout-xs=column layout-sm=column flex=100><div layout=column flex=40><label style=\"margin-bottom: 2%\">{{\'label-natureza-imovel-permitido\' | translate}} *</label><div layout=row style=\"margin-left: 1.5%\"><md-checkbox ng-required=!fundamentoLegal.isNaturezaRural class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isNaturezaUrbana name=\"{{\'label-urbano\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-urbano\' | translate}}</label></md-checkbox><md-checkbox ng-required=!fundamentoLegal.isNaturezaUrbana class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isNaturezaRural name=\"{{\'label-rural\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-rural\' | translate}}</label></md-checkbox></div></div><div layout=column flex=60><label>{{\'label-situacao-incorporacao-imovel-permitido\' | translate}} *</label><div layout=row><md-checkbox ng-required=\"!fundamentoLegal.isincorporado && !fundamentoLegal.isNaoSeAplica\" class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isEmProcessoIncorporacao name=\"{{\'label-em-progresso-incorporacao\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-em-progresso-incorporacao\' | translate}}</label></md-checkbox><md-checkbox ng-required=\"!fundamentoLegal.isEmProcessoIncorporacao && !fundamentoLegal.isNaoSeAplica\" class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isincorporado name=\"{{\'label-incorporado\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-incorporado\' | translate}}</label></md-checkbox><md-checkbox ng-if=\"fundamentoLegal.isPermitirImoveisEspelhoDAgua || fundamentoLegal.isPermitirImoveisCavidadeNatural\" ng-checked=true class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isNaoSeAplica name=\"{{\'label-nao-se-aplica\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-nao-se-aplica\' | translate}}</label></md-checkbox></div></div></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=50 class=md-block><label>{{\'label-forma-incorporacao-permitida\'| translate}}</label><md-select ng-model=fundamentoLegal.formaDeIncorporacaoPermitidas ng-model-options=\"{trackBy:\'$value.descricao\'}\" multiple required ng-disabled=\"bloquear || isDetalhar\"><md-input-container style=\"margin: 0\"><md-checkbox ng-model=todasFormasIncorporacao class=md-primary ng-change=selecionarTodasFormas(todasFormasIncorporacao)>{{\'label-todos\' | translate}}</md-checkbox></md-input-container><md-option ng-repeat=\"itemIncorporacao in listaIncorporacao\" ng-value=itemIncorporacao>{{itemIncorporacao.descricao}}</md-option></md-select></md-input-container><md-input-container style=\"margin-top: 0px\" flex=50 class=md-block ng-if=mostrarTipoAquisicao()><label>{{\'label-tipo-aquisicao-permitida\'| translate}}</label><md-select multiple ng-model=fundamentoLegal.tipoAquisicaoPermitidas ng-model-options=\"{trackBy:\'$value.descricao\'}\" required ng-disabled=\"bloquear || isDetalhar\"><md-input-container style=\"margin: 0\"><md-checkbox ng-model=todasTipoAquisicao class=md-primary ng-change=selecionarTodasTiposAquisicao(todasTipoAquisicao)>{{\'label-todos\' | translate}}</md-checkbox></md-input-container><md-option ng-repeat=\"itemAquisicao in listaAquisicao\" ng-value=itemAquisicao>{{itemAquisicao.descricao}}</md-option></md-select></md-input-container></div><div layout=row flex-offset=0 class=margin-top layout-wrap ng-if=mostrarEntidadeExtinta()><md-input-container style=\"margin-top: 0px\" flex=100 class=md-block><label>{{\'label-entidade-extinta\'| translate}}</label><md-select multiple ng-model=fundamentoLegal.entidadeExtintas ng-model-options=\"{trackBy:\'$value.descricao\'}\" required ng-disabled=\"bloquear || isDetalhar\"><md-input-container style=\"margin: 0\"><md-checkbox ng-model=todasEntidadesExtintas class=md-primary ng-change=selecionarTodasEntidadesExtintas(todasEntidadesExtintas)>{{\'label-todos\' | translate}}</md-checkbox></md-input-container><md-option ng-repeat=\"itemEntidade in listaEntidadesExtintas\" ng-value=itemEntidade>{{itemEntidade.descricao}}</md-option></md-select></md-input-container></div><br><div layout=row flex><h3>{{\'label-avaliacao-imovel\' | translate}}</h3></div><div layout=row flex-offset=0 class=margin-top layout-wrap style=\"margin-bottom: 20px !important\"><md-input class=md-block flex layout=column><label>{{\'label-definicao-metodologia-avaliacao\' | translate}} *</label><md-radio-group style=\"margin: 2% 0 0 2%\" class=md-block ng-model=fundamentoLegal.metodologiaAvaliacao layout=row name=\"{{\'label-inclur-area-fracao-utilizada-por-responsavel\' | translate}}\" ng-disabled=isDetalhar><md-radio-button class=md-primary ng-value=true><label>{{\'label-ordem-prioridade\' | translate}}</label></md-radio-button><md-radio-button class=md-primary ng-value=false><label>{{\'label-maior-valor\' | translate}}</label></md-radio-button></md-radio-group></md-input></div><div layout=row flex-offset=0 layout-wrap ng-show=\"fundamentoLegal.metodologiaAvaliacao == true || fundamentoLegal.metodologiaAvaliacao == false\"><md-input-container style=\"margin-top: 20px\" flex=50 class=md-block><label>{{\'label-tipo-avaliacao\'| translate}}</label><md-select ng-model=fundamentoLegal.tipoAvaliacao ng-disabled=isDetalhar><md-option ng-repeat=\"tipoAvaliacao in listaTiposAvaliacao\" ng-value=tipoAvaliacao>{{tipoAvaliacao.nome}}</md-option></md-select></md-input-container><md-input-container style=\"margin-top: 10px\" flex=20 class=md-block><md-button class=\"md-raised margin-botao-footer\" ng-click=adicionarTipoAvaliacao(fundamentoLegal.tipoAvaliacao) ng-disabled=isDetalhar>{{\'btn-adicionar\' | translate | uppercase}}</md-button></md-input-container></div><md-content ng-show=\"paginationFundamentosLegaisTipoAvaliacao.length > 0\"><md-table-container class=\"md-block margin-top-2-em\" flex-gt-xs flex=100><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row class=upload-head><th style=\"width: 30px\" md-column ng-show=\"fundamentoLegal.metodologiaAvaliacao == true && !isDetalhar\"></th><th style=\"width: 50px\" md-column ng-show=\"fundamentoLegal.metodologiaAvaliacao == true\">{{\'label-tabela-ordem\' | translate}}</th><th md-column>{{\'label-tabela-avaliacao\' | translate}}</th><th style=\"width: 5%\" md-column ng-show=!isDetalhar>{{\'label-tabela-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"fundamentoLegalTipoAvaliacao in paginationFundamentosLegaisTipoAvaliacao track by $index\"><td md-cell width=30px class=column-acao ng-show=\"fundamentoLegal.metodologiaAvaliacao == true && !isDetalhar\"><md-button class=\"md-fab md-mini button-acao button-acao-up\" ng-click=\"decrementarOrdem(paginationFundamentosLegaisTipoAvaliacao, fundamentoLegalTipoAvaliacao.ordem)\"><md-icon ng-bind=\"\'expand_less\'\" style=\"color: #000000\"></md-icon></md-button><md-button class=\"md-fab md-mini button-acao button-acao-down\" ng-click=\"incrementarOrdem(paginationFundamentosLegaisTipoAvaliacao, fundamentoLegalTipoAvaliacao.ordem)\"><md-icon ng-bind=\"\'expand_more\'\" style=\"color: #000000\"></md-icon></md-button></td><td md-cell ng-show=\"fundamentoLegal.metodologiaAvaliacao == true\">{{fundamentoLegalTipoAvaliacao.ordem}}</td><td md-cell>{{fundamentoLegalTipoAvaliacao.tipoAvaliacao.nome}}</td><td md-cell ng-show=!isDetalhar><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><a href=\"\" ng-click=removerTipoAvaliacao($index) class=\"md-fab md-mini\"><ng-md-icon icon=delete style=fill:#788489 size=25 alt=\"{{\'label-excluir-tipo-avaliacao\' | translate}}\"><md-tooltip>{{\'label-excluir-tipo-avaliacao\' | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" style=\"border: 1px solid #cccccc; margin:auto; height: 63px\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoTipoAvaliacao md-page-select></md-table-pagination></md-content><div layout=column layout-xs=column layout-sm=column flex=100><md-checkbox class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.valorPercentual name=\"{{\'label-definir-percentual-valor\' | translate}}\" flex=100 ng-disabled=isDetalhar><label>{{\'label-definir-percentual-valor\' | translate}}</label></md-checkbox></div><div layout=row ng-show=fundamentoLegal.valorPercentual><md-input-container flex=50><label>{{\'label-valor-percentual\' | translate}}</label><input name=\"{{\'label-valor-percentual\' | translate}}\" ng-model=fundamentoLegal.percentual ui-number-mask=2 maxlength=6 ng-change=validaPercentual(fundamentoLegal.percentual) ng-required=\"fundamentoLegal.valorPercentual == true\" ng-disabled=isDetalhar><md-icon md-font-set=regular-font>%</md-icon></md-input-container></div></div></div><div class=button-botton layout=row layout-align=\"end center\"><md-button class=\"md-raised margin-botao-footer\" ng-click=fecharFundamentoLegal()>{{\'btn-fechar\' | translate | uppercase}}</md-button><md-button class=md-raised ng-show=!isDetalhar ng-click=salvarFundamentoLegal(formularioDadosFundamentoLegal)>{{\'btn-salvar\'| translate | uppercase}}</md-button></div></md-content></ng-form>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/consultarFundamentoLegal.html","<md-content><h2 class=tituloFormulario>{{\'label-consultar-fundamento-legal\' | translate}}</h2><md-dialog-content><md-input-container class=\"md-block margin-top-2-em\" flex-gt-xs flex=100><label>{{\'label-instrumento-funcionalidade\' | translate}}</label><md-select ng-model=filtro.idInstrumentoFuncionalidade ng-disabled=isEditar name=\"{{\'label-instrumento-funcionalidade\' | translate}}\"><md-option ng-repeat=\"instrumento in listaInstrumentosFuncionalidade\" ng-value=instrumento.instrumentoFuncionalidade.id>{{instrumento.instrumentoFuncionalidade.modulo.descricao + \' / \' + instrumento.instrumentoFuncionalidade.descricao + \' / \' + instrumento.titulo}}</md-option></md-select></md-input-container></md-dialog-content><md-dialog-content><md-input-container class=md-block flex-gt-xs flex=100><label>{{\'label-fundamento-legal\' |translate}}</label><textarea ng-model=filtro.template ui-mask-placeholder-char=space md-maxlength=100 maxlength=100 name=\"{{\'label-fundamento-legal\' | translate}}\"></textarea></md-input-container></md-dialog-content><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=voltar()>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised ng-click=limparFiltroConsulta()>{{\'btn-limpar\' | translate}}</md-button><md-button class=md-raised ng-click=pesquisarFundamentos()>{{\'btn-pesquisar\' | translate}}</md-button><md-button class=md-raised ng-click=cadastrarFundamento()>{{\'btn-novo\' | translate}}</md-button></section></div><!--- fim dos botoes --><md-content ng-show=\"listaFundamentosLegais.length > 0\"><md-table-container class=\"md-block margin-top-2-em\" flex-gt-xs flex=100><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-instrumento-funcionalidade\' | translate}}</th><th md-column>{{\'label-fundamento-legal\' | translate}}</th><th md-column>{{\'label-status\' | translate}}</th><th md-column>{{\'label-acao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"fundamento in listaFundamentosLegais\"><td md-cell><label ng-bind=\"fundamento.modulo + \'/\' + fundamento.instrumento\"></label></td><td md-cell><label ng-bind=fundamento.descricaoFundamento></label></td><td md-cell><label ng-bind=\"fundamento.ativo ? \'Ativo\' : \'Cancelado\'\"></label></td><td md-cell><md-fab-speed-dial style=\"left: -55px; position: relative\" md-direction=left class=\"hand md-scale alinhar-md-fab-speed-dial\" layout-align=\"end end\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><a href=\"\" ng-if=fundamento.ativo ng-click=redirecionarEditarFundamentoLegal(fundamento)><ng-md-icon class=icon-subacao icon=create style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'btn-editar\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-if=fundamento.ativo ng-click=cancelarFundamentoLegal(fundamento)><ng-md-icon class=icon-subacao icon=cancel style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'label-cancelar-fundamento-legal\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=detalharFundamentoLegal(fundamento)><ng-md-icon class=icon-subacao icon=search style=fill:#788489 size=25><md-tooltip>{{\"label-detalhar\" | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" style=\"border: 1px solid #cccccc; margin:auto; height: 63px\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarFundamentos md-page-select></md-table-pagination></md-content></md-content>");
$templateCache.put("scripts-integrador/pages/geral/view/header.html","<!-- Barra de navageção --><md-toolbar layout=row class=md-whiteframe-z3 ng-controller=\"MenuController as ul\"><!-- hide-gt-sm --><md-button class=menu ng-click=ul.toggleMenu() aria-label=\"Menu da Aplicação\"><ng-md-icon icon=menu style=fill:#fff size=38></ng-md-icon></md-button><div layout=column hide-xs layout-align=\"center center\"><a ui-sref=INTEGRADOR.HOME><img src=assets/img/logo-spu.png></a></div><div layout=row flex=75 layout-align=\"end center\"><a ui-sref=INTEGRADOR.HOME class=posicao-itens-barra-menu><ng-md-icon icon=home style=fill:#fff size=35 alt=Home><md-tooltip md-direction=bottom>Página inicial</md-tooltip></ng-md-icon></a><ng-md-icon icon=notifications class=posicao-itens-barra-menu size=35 alt=Notificações><md-tooltip md-direction=bottom>Notificações</md-tooltip></ng-md-icon><ng-md-icon icon=help class=posicao-itens-barra-menu size=35 alt=Ajuda><md-tooltip md-direction=bottom>Ajuda</md-tooltip></ng-md-icon><!--<a href=\"/api/saml/logout\">--><ng-md-icon icon=do_not_disturb class=posicao-itens-barra-menu size=35 alt=Notificações><md-tooltip md-direction=bottom>Logout</md-tooltip></ng-md-icon><!--</a>--><md-button class=\"md-fab md-primary md-hue-2\" ng-click=$mdMenu.open($event) aria-label=Configurações><md-tooltip md-direction=left>Configurações da aplicação</md-tooltip><md-icon>person</md-icon></md-button></div></md-toolbar><md-toolbar layout=row class=md-whiteframe-z3 style=\"min-height: 32px !important; background: #38813C !important\"><div flex=60 ncy-breadcrumb></div><!--<div flex=\"60\" style=\"text-align: right;\" >\n        <md-input-container style=\"width: 40%; text-align: center;margin-bottom: 1%;margin-top: 1%; font-size: large;\" class=\"favoritas\" layout-align=\"end center\">\n            <md-select style=\"padding-top: 0px;padding-bottom: 0px;\" ng-model=\"teste\" aria-label=\"tetsts\" placeholder=\"Funções favoritas\">\n                <md-option ng-value=\"favoritas\">Funções favoritas</md-option>\n            </md-select>\n        </md-input-container>\n    </div>\n    --><div flex layout=row layout-align=\"end center\"><contador-sessao></contador-sessao></div><!-- Container Menu e Conteúdo --></md-toolbar><!-- MENU --><menu>");
$templateCache.put("scripts-integrador/pages/geral/view/template.html","<div flex layout=column><!-- Conteúdo Principal --><div ng-include=\"\'scripts-integrador/pages/geral/view/header.html\'\"></div><div style=\"overflow:auto; height: 100% !important\"><md-content flex layout=column><div ui-view></div></md-content></div></div>");
$templateCache.put("scripts-integrador/pages/geral/view/usuario-erro-login.tmpl.html","<div class=padding-right-pagina><div class=md-title-com-borda>Ocorreu um erro ao acessar seus dados de login</div><h2 style=\"margin-top: 50px; text-align: center\">Erro de Autenticação!</h2><h3 style=\"text-align: center; line-height: 200%; margin-top: 50px\">Aguarde um momento e tente novamente.<br><br>Em caso de persistir o erro, favor encaminhar para o administrador</h3></div>");
$templateCache.put("scripts-integrador/pages/home/view/geovis.html","<div flex=100 id=pesquisa_barra layout-xs=column style=\"margin-top: 20px\"><div class=\"home-box pesquisa\"><header class=home-box-header><h2><b>Geovis</b></h2></header><div id=homeGeovis class=homeGeovis><div class=gv-map-overlay><form name=form-pesquisar layout=row><input name=busca ng-model=homeCtrl.buscaGeovis placeholder=Pesquisar flex> <a ng-click=homeCtrl.geovisPesquisar(); style=cursor:pointer><ng-md-icon icon=search></ng-md-icon></a></form></div><div id=homeMapGeovis class=homeMapGeovis></div></div></div></div>");
$templateCache.put("scripts-integrador/pages/home/view/graficos.html","<div><div style=\"margin-top: 20px\" flex-xs=100 flex class=\"home-box carteira-imobiliaria\"><header class=home-box-header><h2><b>CARTEIRA IMOBILIÁRIA</b></h2></header><div layout=row layout-xs=column layout-align-xs=\"center center\"><div flex=33 flex-xs=100><highchart id=graficoQuantidade config=homeCtrl.opcoesGraficoQuantidade></highchart></div><div flex=33 flex-xs=100><highchart id=graficoArea config=homeCtrl.opcoesGraficoArea></highchart></div><div flex=33 flex-xs=100><highchart id=graficoValor config=homeCtrl.opcoesGraficoValor></highchart></div></div></div></div><div layout=column layout-align=\"center end\"><md-button class=color-carteira-imobiliaria><span>+ Ver Painel</span><md-icon class=icon-forward-carteira-imobiliaria md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div>");
$templateCache.put("scripts-integrador/pages/home/view/home.tmpl.html","<div layout=column ng-cloak flex=100><md-content style=\"margin-top: 10px; background-color: #f7f7f7\"><div ng-include=\"\'scripts-integrador/pages/home/view/pendencias.html\'\"></div><div style=\"display: flex; flex-wrap: wrap; justify-content: space-between\"><div ng-include=\"\'scripts-integrador/pages/home/view/informes.html\'\" hide-xs hide-sm style=\"width: 65%\"></div><div ng-include=\"\'scripts-integrador/pages/home/view/informesMobile.html\'\" hide-md hide-gt-md></div><!-- Mapa Geovis --><div ng-include=\"\'scripts-integrador/pages/home/view/geovis.html\'\" class=ng-scope style=\"width: 35%;flex-grow: 1\"></div></div><div flex layout=row layout-gt-md=row layout-xs=column layout-md=column layout-gt-xs=column><div ng-include=\"\'scripts-integrador/pages/home/view/graficos.html\'\" flex=65 flex-xs=100 flex-md=100 flex-sm=100 flex-gt-xs=100 flex-gt-md=65></div><div ng-include=\"\'scripts-integrador/pages/home/view/painelServicos.html\'\" flex=35 flex-xs=100 flex-md=100 flex-sm=100 flex-gt-xs=100 flex-gt-md=35></div></div><div layout=row><div layout=column style=\"margin-top: 20px\"><!--<div layout=\"row\" layout-xs=\"column\">\n\n					<div layout=\"column\" layout-xs=\"column\" flex >\n						<div class=\"home-box msg\" style=\"height: 100%;\">\n							<header class=\"home-box-header\" style=\"width: 100%; \">\n								<h2 style=\"width: 100%;\">\n									<b>MENSAGENS</b>\n									<b style=\"float: right; margin-right: 28px; border: 1px black;\">\n										{{homeCtrl.quantidadeMensagensNaoLidas}}\n									</b>\n								</h2>\n							</header>\n							<section>\n								<md-content>\n									<p class=\"mensagem hover\" ng-repeat=\"item in homeCtrl.mensagens | limitTo : 11\"\n									   ng-model-options=\"{trackBy: \'$value.codigo\'}\"\n									   ng-click=\"homeCtrl.mensagemLida($index); homeCtrl.showMensagemDialog($event, $index)\">\n\n										<a ng-class=\"(item.lida == false ? \'msg-nao-lida\' : \'msg-lida\')\" href=\"#\" >\n											{{item.dataEnvio | date : \'dd/MM/yyyy\'}} - {{item.assunto}}\n										</a>\n									</p>\n								</md-content>\n							</section>\n\n						</div>\n						<div>\n							<md-button style=\"float: right;\" class=\"color-mensagens\" ng-click=\"homeCtrl.showMensagensDialog($event)\"><span>+ Mensagens</span>\n								<md-icon class=\"icon-forward-mensagens\" md-svg-src=\"assets/img/icons/arrow-point-to-right.svg\"></md-icon>\n							</md-button>\n						</div>\n\n					</div>\n\n				</div>--><!--<div flex layout=\"row\" >\n					<div  ng-include=\"\'scripts-integrador/pages/home/view/informes.html\'\"></div>\n					<div  ng-include=\"\'scripts-integrador/pages/home/view/pesquisaImovel.html\'\"></div>\n				</div>--><!--<div flex layout=\"row\">\n					<div ng-include=\"\'scripts-integrador/pages/home/view/graficos.html\'\"></div>\n					<div flex=\"35\" layout-xs=\"column\" style=\"margin-top: 20px; margin-left: 17px\">\n						<div class=\"home-box pesquisa\" flex>\n							<header class=\"home-box-header\">\n								<h2><b>CONSULTA DE SERVIÇOS</b></h2>\n							</header>\n							Teste\n						</div>\n					</div>\n				</div>--></div></div><!--<div style=\"margin-top: 20px;\" layout=\"row\" layout-xs=\"column\" flex>\n			<div layout=\"row\" layout-xs=\"column\" flex class=\"home-box videos-box\" >\n				<div layout=\"column\" layout-xs=\"column\" flex=\"100\" layout-xs=\"column\">\n					<header class=\"home-box-header\">\n						<h2><b>VÍDEOS</b></h2>\n					</header>\n					<md-content flex layout=\"row\" layout-xs=\"column\">\n						<div class=\"margem-3\"><iframe width=\"100%\" height=\"85\" src=\"https://www.youtube.com/embed/UQoD3W_Sp5s\" frameborder=\"0\" allowfullscreen></iframe></div>\n						<div class=\"margem-3\"><iframe width=\"100%\" height=\"85\" src=\"https://www.youtube.com/embed/KqnjNSVpps0\" frameborder=\"0\" allowfullscreen></iframe></div>\n						<div class=\"margem-3\"><iframe width=\"100%\" height=\"85\" src=\"https://www.youtube.com/embed/KqnjNSVpps0\" frameborder=\"0\" allowfullscreen></iframe></div>\n						<div class=\"margem-3\"><iframe width=\"100%\" height=\"85\" src=\"https://www.youtube.com/embed/7d0617fcJuk\" frameborder=\"0\" allowfullscreen></iframe></div>\n					</md-content>\n				</div>\n			</div>\n\n			<div layout=\"row\" layout-xs=\"column\" flex style=\"overflow-x: hidden;\">\n				<div layout=\"column\" layout-xs=\"column\" flex=\"100\" class=\"home-box links-uteis-box\" layout-xs=\"column\" style=\"overflow-x: hidden;\">\n					<header class=\"home-box-header\">\n						<h2><b>LINKS ÚTEIS</b></h2>\n					</header>\n					<md-content flex style=\"overflow-x: hidden;\" layout-xs=\"column\">\n						<div class=\"links-uteis-content\"><a href=\"http://www.planejamento.gov.br/\" target=\"_blank\"> <i class=\"fa fa-link fa-3x\" aria-hidden=\"true\"></i><p><strong>MP</strong></p></a></div>\n						<div class=\"links-uteis-content\"><a href=\"http://patrimoniodetodos.gov.br/\" target=\"_blank\"><i class=\"fa fa-link fa-3x\" aria-hidden=\"true\"></i><p><strong>SPU</strong></p></a></div>\n						<div class=\"links-uteis-content\"><a href=\"https://sei.mc.gov.br\" target=\"_blank\"><i class=\"fa fa-link fa-3x\" aria-hidden=\"true\"></i><p><strong>SEI</strong></p></a></div>\n						<div class=\"links-uteis-content\"><a href=\"https://sisrei.spu.planejamento.gov.br/sisrei/\" target=\"_blank\"><i class=\"fa fa-link fa-3x\" aria-hidden=\"true\"></i><p><strong>SISREI</strong></p></a></div>\n						<div class=\"links-uteis-content\"><a href=\"http://www.pgfn.fazenda.gov.br/\" target=\"_blank\"><i class=\"fa fa-link fa-3x\" aria-hidden=\"true\"></i><p><strong>PGFN</strong></p></a></div>\n\n					</md-content>\n\n				</div>\n			</div>\n\n\n		</div>--></md-content></div><div ng-if=urlServicos><iframe id=frame ng-src={{urlServicos}} width=1px style=\"display: none\"></iframe></div><script>setTimeout(function(){ $(\'#frame\').remove();}, 10000);</script>");
$templateCache.put("scripts-integrador/pages/home/view/informes.html","<div layout=row><div style=\"margin-top: 20px\" flex-xs=100 class=\"home-box infos\"><header class=home-box-header><h2 class=informe-header><b>INFORMES</b></h2></header><div><div layout=row><div layout=column flex=50 layout-margin=5 ng-repeat=\"item in homeCtrl.informesHome | limitTo : 1\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><div ng-if=item.imagem class=informe-caixa-img><img ng-src={{homeCtrl.gerarSrcImagem(item.imagem)}} height=150 width=300></div><div><div class=informe-data>{{item.dataPublicacao | date : \'dd/MM/yyyy\'}}</div><h2 class=informe-titulo>{{item.titulo}}</h2><div class=informe-descricao ng-bind-html=homeCtrl.descricaoExtensa(item.corpo)></div><div layout=row layout-align=\"end end\"><md-button class=btn-continue-lendo-primeira-noticia ng-click=\"homeCtrl.continuarLendo(item); homeCtrl.showInformesDialog($event)\">continue lendo</md-button></div></div></div><div layout=column flex=50 layout-margin=5><div layout=column flex=30 class=informe-item ng-repeat=\"item in homeCtrl.informesHome | limitTo : 2:1\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><div class=informe-data>{{item.dataPublicacao | date : \'dd/MM/yyyy\'}}</div><h2 class=informe-titulo>{{item.titulo}}</h2><div class=informe-descricao ng-bind-html=homeCtrl.descricaoExtensa(item.corpo)></div><div layout=row layout-align=\"end end\"><md-button class=btn-continue-lendo ng-click=\"homeCtrl.continuarLendo(item); homeCtrl.showInformesDialog($event)\">continue lendo</md-button></div></div></div></div><!-- <div layout=\"column\" flex=\"30\" class=\"informe-item\" ng-repeat=\"item in homeCtrl.informes | limitTo : 2\"\n            ng-model-options=\"{trackBy: \'$value.codigo\'}\" >\n           <p class=\"informe-data\">{{item.data | date : \'dd/MM/yyyy\'}}</p>\n           <h2 class=\"informe-titulo\">{{item.titulo}}</h2>\n           <p class=\"informe-descricao\">{{homeCtrl.descricaoExtensa(item.descricao)}}\n               <md-button class=\"btn-continue-lendo\"\n                          ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo\n               </md-button>\n           </p>\n\n       </div>\n--><!--  <div class=\"informe-item\" ng-repeat=\"item in homeCtrl.informes | limitTo : 3\"\n             ng-model-options=\"{trackBy: \'$value.codigo\'}\">\n            <p class=\"informe-data\">{{item.data | date : \'dd/MM/yyyy\'}}</p>\n            <h2 class=\"informe-titulo\">{{item.titulo}}</h2>\n            <p class=\"informe-descricao\">{{homeCtrl.descricaoExtensa(item.descricao)}}\n                <md-button class=\"btn-continue-lendo\"\n                           ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo\n                </md-button>\n            </p>\n\n        </div>--></div></div><!--  <div flex=\"35\" style=\"margin-top: 20px; margin-left:1%\" class=\"home-box pesquisa\">\n    <header class=\"home-box-header\">\n      <h2><b>PESQUISA DE IMÓVEL</b></h2>\n    </header>\n    <div flex=\"100\" class=\"pesquisa-img-container\">\n      <img src=\"/assets/img/mapa_imovel.png\" class=\"img-thumbnail img-circle img-responsive\">\n      <a href=\"https://imoveisfederais.planejamento.gov.br/spin-web/login\" target=\"_blank\">\n        <i class=\"fa fa-search fa-5x\" aria-hidden=\"true\" style=\"margin-top: 30%;\"></i>\n      </a>\n    </div>\n  </div> --><!--\n  <div layout=\"column\" layout-xs=\"column\" flex=\"65\" style=\"margin-right: 20px;\">\n      <div class=\"home-box infos\" style=\"width: 100% !important; height: 100%;\">\n          <header class=\"home-box-header\">\n              <h2 class=\"informe-header\"><b>INFORMES</b></h2>\n          </header>\n          <md-content style=\"height: 82%;\" class=\"no-scrollbar-y\">\n\n              <div class=\"informe-item\" ng-repeat=\"item in homeCtrl.informes | limitTo : 2\"\n                   ng-model-options=\"{trackBy: \'$value.codigo\'}\">\n                  <p class=\"informe-data\">{{item.data | date : \'dd/MM/yyyy\'}}</p>\n                  <h2 class=\"informe-titulo\">{{item.titulo}}</h2>\n                  <p class=\"informe-descricao\">{{homeCtrl.descricaoExtensa(item.descricao)}}\n                      <md-button class=\"btn-continue-lendo\"\n                                 ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo\n                      </md-button>\n                  </p>\n\n              </div>\n\n          </md-content>\n      </div>\n      <div>\n          <md-button style=\"float: right;margin-right: 0\" class=\"color-infos\" ng-click=\"homeCtrl.resetInformeSelecionado(); homeCtrl.showInformesDialog($event)\"><span>+ Informes</span>\n              <md-icon class=\"icon-forward-infos\" md-svg-src=\"assets/img/icons/arrow-point-to-right.svg\"></md-icon>\n          </md-button>\n      </div>\n  </div>--></div><div><div layout=column layout-align=\"end end\" layout-align=\"end end\"><md-button class=color-infos ng-click=\"homeCtrl.resetInformeSelecionado(); homeCtrl.showInformesDialog($event)\"><span>+ Informes</span><md-icon class=icon-forward-infos md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div><!--   <div flex=\"35\" layout=\"column\" style=\"margin-left:1%;\" layout-align=\"end end\"> --><!--     <md-button class=\"color-pesq-imoveis\" ng-href=\"https://imoveisfederais.planejamento.gov.br/spin-web/login\" --><!--                target=\"_blank\"><span>+ Clique Aqui</span> --><!--       <md-icon class=\"icon-forward-pesq-imoveis\" md-svg-src=\"assets/img/icons/arrow-point-to-right.svg\"></md-icon> --><!--     </md-button> --><!--   </div> --></div>");
$templateCache.put("scripts-integrador/pages/home/view/informesMobile.html","<div style=\"margin-top: 20px\"><div flex-xs=100 flex class=\"home-box infos\"><header class=home-box-header><h2 class=informe-header><b>INFORMES</b></h2></header><div><div layout=row layout-xs=column><div layout=column flex-xs=100 flex=50 flex-sm=100 ng-repeat=\"item in homeCtrl.informesHome | limitTo : 1\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><div ng-if=item.imagem class=informe-caixa-img><img ng-src={{homeCtrl.gerarSrcImagem(item.imagem)}} height=150 width=300></div><div><div class=informe-data>{{item.dataPublicacao | date : \'dd/MM/yyyy\'}}</div><div class=informe-titulo>{{item.titulo}}</div><div class=informe-descricao-mobile>{{homeCtrl.descricaoExtensa(item.corpo)}}<md-button class=btn-continue-lendo ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo</md-button></div></div></div><div layout=column flex=50 flex-xs=100 hide-sm><div layout=column flex-xs=100 flex=30 ng-repeat=\"item in homeCtrl.informesHome | limitTo : 2:1\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><div class=informe-data>{{item.dataPublicacao | date : \'dd/MM/yyyy\'}}</div><div class=informe-titulo>{{item.titulo}}</div><div class=informe-descricao-mobile>{{homeCtrl.descricaoExtensa(item.corpo)}}<md-button class=btn-continue-lendo ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo</md-button></div></div></div></div></div></div></div><div layout=column layout-align=\"end end\"><md-button class=color-infos ng-click=\"homeCtrl.resetInformeSelecionado(); homeCtrl.showInformesDialog($event)\"><span>+ Informes</span><md-icon class=icon-forward-infos md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div>");
$templateCache.put("scripts-integrador/pages/home/view/painelServicos.html","<div flex=100 layout-xs=column style=\"margin-top: 20px; margin-left: 17px\" flex-xs=100><div class=\"home-box painel-servicos\" flex><header class=home-box-header><h2><b>PAINEL DE SERVIÇOS</b></h2></header><div layout-md=row layout-sm=row><div layout=column class=painel-servicos-box flex-md=50 flex-sm=50><div layout=row class=painel-servicos-box-itens><div layout=row flex=25><i class=\"fa fa-files-o fa-3x amarelo\" aria-hidden=true></i></div><div flex=35>Total de Requerimentos</div><div flex=40 class=painel-servicos-box-valores>{{homeCtrl.dadosPainelServico.totalRequerimentos}}</div></div></div><div layout=column class=painel-servicos-box flex-md=50 flex-sm=50><div layout=row class=painel-servicos-box-itens><div layout=row flex=25><i class=\"fa fa-exclamation-triangle fa-3x vermelho\" aria-hidden=true></i></div><div flex=35>Número de Requerimentos Pendentes</div><div flex=40 class=painel-servicos-box-valores>{{homeCtrl.dadosPainelServico.totalPendentes}}</div></div></div></div><div layout-md=row layout-sm=row><div layout=column class=painel-servicos-box flex-md=50 flex-sm=50><div layout=row class=painel-servicos-box-itens><div layout=row flex=25><i class=\"fa fa-tachometer fa-3x azul\" aria-hidden=true></i></div><div flex=35>Percentual de Requerimentos Pendentes</div><div flex=40 class=painel-servicos-box-valores>{{homeCtrl.dadosPainelServico.percentualPendentes}} %</div></div></div><div layout=column class=painel-servicos-box flex-md=50 flex-sm=50><div layout=row class=painel-servicos-box-itens><div layout=row flex=25><i class=\"fa fa-clock-o fa-3x verde\" aria-hidden=true></i></div><div flex=35>Tempo Médio de Atendimento</div><div flex=40 class=painel-servicos-box-valores>{{homeCtrl.dadosPainelServico.prazoMedio}} dias</div></div></div></div></div></div><div layout=column layout-align=\"end end\"><md-button class=color-painel-servicos target=_blank href=http://painel.su.spu.planejamento.gov.br><span>+ Ver Painel</span><md-icon class=icon-forward-painel-servicos md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div>");
$templateCache.put("scripts-integrador/pages/home/view/pendencias.html","<div layout=row layout-xs=column><div layout=column flex=100 class=\"home-box pendencias-box\" layout-xs=column><header class=home-box-header><h2><b>LISTA DE PENDÊNCIAS</b></h2></header><section><div layout=row layout-xs=column layout-align=center class=pendencias-geral><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div ng-style=servicoStyle id=pendenciasServicos class=pendencia-item ng-class=\"homeCtrl.pendenciasServicos.quantidadeTotalPendencias > 0 ? \'pendencia-item-servico\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 1);\" verificar-permissao-pendencia=\"SERVICOS.MANTER_SERVICO, SERVICOS.EXCLUIR_SERVICO, SERVICOS.CONSULTAR_SERVICO,\n                         SERVICOS.CONSULTAR_TRIAGEM,SERVICOS.EFETUAR_ANALISE_TECNICA,SERVICOS.VISUALIZAR_ANALISE_TECNICA,SERVICOS.CONCLUIR_ANALISE\"><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasServicos.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/servicos-icon.png style=\"opacity: 0.3;  width:30%\"></div></div><a href=# class=pendecia-footer>SERVIÇOS</a></div></md-content><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div class=pendencia-item ng-class=\"homeCtrl.pendenciasCadastroImovel.quantidadeTotalPendencias > 0 ? \'pendencia-item-cadastro\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 2);\" verificar-permissao-pendencia=PERMISSAO_CADASTRO><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasCadastroImovel.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/cadastro-icon.png style=\"opacity: 0.2; margin-right: 5px; width:30%\"></div></div><a href=# class=pendecia-footer>CADASTRO</a></div></md-content><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div class=pendencia-item ng-class=\"homeCtrl.pendenciasAvaliacaoContabilidade.quantidadeTotalPendencias > 0 ? \'pendencia-item-avaliacao\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 3);\" verificar-permissao-pendencia=PERMISSAO_AVALIACAO><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasAvaliacaoContabilidade.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/avaliacao-icon.png style=\"opacity: 0.2; margin-right: 5px; width:23%\"></div></div><a href=# class=pendecia-footer>AVALIAÇÃO</a></div></md-content><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div class=pendencia-item ng-class=\"homeCtrl.pendenciasDestinacao.quantidadeTotalPendencias > 0 ? \'pendencia-item-destinacao\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 4);\" verificar-permissao-pendencia=\"DESTINACAO.DESTINACAO_CANCELAR_CESSAO_GRATUITA, DESTINACAO.DESTINACAO_CANCELAR_DOACAO,\n                         DESTINACAO.DESTINACAO_CANCELAR_VENDA, DESTINACAO.DESTINACAO_CANCELAR_POSSE_INFORMAL, DESTINACAO.DESTINACAO_CANCELAR_CUEM,\n                         DESTINACAO.DESTINACAO_CANCELAR_CDRU, DESTINACAO.DESTINACAO_CANCELAR_TERMO_ENTREGA, DESTINACAO.DESTINACAO_CANCELAR_USO_PROPRIO,\n                         DESTINACAO.DESTINACAO_CANCELAR_PERMISSAO_USO_IMOVEL_FUNCIONAL, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_CESSAO_GRATUITA,\n                         DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_DOACAO, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_VENDA, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_POSSE_INFORMAL,\n                         DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_CUEM, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_CDRU, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_TERMO_ENTREGA,\n                         DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_USO_PROPRIO, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_PERMISSAO_USO_IMOVEL_FUNCIONAL, DESTINACAO.DESTINACAO_ENCERRAR_CESSAO_GRATUITA,\n                         DESTINACAO.DESTINACAO_ENCERRAR_DOACAO, DESTINACAO.DESTINACAO_ENCERRAR_VENDA, DESTINACAO.DESTINACAO_ENCERRAR_POSSE_INFORMAL, DESTINACAO.DESTINACAO_ENCERRAR_CUEM,\n                         DESTINACAO.DESTINACAO_ENCERRAR_CDRU, DESTINACAO.DESTINACAO_ENCERRAR_TERMO_ENTREGA, DESTINACAO.DESTINACAO_ENCERRAR_USO_PROPRIO, DESTINACAO.DESTINACAO_ENCERRAR_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                         DESTINACAO.DESTINACAO_ENCERRAR_CESSAO_ONEROSA, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_CESSAO_GRATUITA, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_DOACAO,\n                         DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_VENDA, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_POSSE_INFORMAL, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_CUEM,\n                         DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_CDRU, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_TERMO_ENTREGA, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_USO_PROPRIO,\n                         DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_PERMISSAO_USO_IMOVEL_FUNCIONAL, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_CESSAO_ONEROSA\"><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasDestinacao.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/destinacao-icon.png style=\"opacity: 0.2; margin-right: 5px; width:30%\"></div></div><a href=# class=pendecia-footer>DESTINAÇÃO</a></div></md-content><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div class=pendencia-item ng-class=\"homeCtrl.pendenciasReceitas.quantidadeTotalPendencias > 0 ? \'pendencia-item-receitas\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 5);\" verificar-permissao-pendencia=PERMISSAO_RECEITAS><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasReceitas.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/receitas-icon.png style=\"opacity: 0.3;  width:30%\"></div></div><a href=# class=pendecia-footer>RECEITAS</a></div></md-content><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div class=pendencia-item ng-class=\"homeCtrl.pendenciasFiscalizacao.quantidadeTotalPendencias > 0 ? \'pendencia-item-fiscalizacao\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 6);\" verificar-permissao-pendencia=PERMISSAO_FISCALIZACAO><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasFiscalizacao.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/fiscalizacao-icon.png style=\"opacity: 0.2;  width:30%\"></div></div><a href=# class=pendecia-footer>FISCALIZAÇÃO</a></div></md-content><!--\n                               <md-content flex class=\"no-scrollbar-y conteudo-pendencias\">\n                                   <div class=\"pendencia-item\" style=\"background-color: #f39c12 !important\" ng-click=\"homeCtrl.showPendenciasDialog($event, 7);\"\n                                   verificar-permissao-pendencia=\"PERMISSAO_GEOINFORMACOES\">\n                                       <div class=\"inner\">\n                                           <p class=\"pendencia-item-contador\">{{homeCtrl.pendenciasGeoInformacoes.quantidadeTotalPendencias  | number : 0}}</p>\n                                           <div class=\"icon\">\n                                               <img src=\"assets/img/icons/geoinformacoes-icon.png\" style=\"opacity: 0.3;  width:30%\"/>\n                                           </div>\n                                       </div>\n                                       <a href=\"#\" class=\"pendecia-footer\">GEOINFORMAÇÕES </a>\n                                   </div>\n                               </md-content>\n               --></div></section></div></div>");
$templateCache.put("scripts-integrador/pages/home/view/pesquisaImovel.html","<div flex=100 layout-xs=column style=\"margin-top: 20px; margin-left: 17px\"><div class=\"home-box pesquisa\"><header class=home-box-header><h2><b>PESQUISA DE IMÓVEL</b></h2></header><div flex=100 class=pesquisa-img-container><img src=/assets/img/mapa_imovel.png class=\"img-thumbnail img-circle img-responsive\"> <a href=https://imoveisfederais.planejamento.gov.br/spin-web/login target=_blank><i class=\"fa fa-search fa-5x\" aria-hidden=true style=\"margin-top: 30%\"></i></a></div><!--<a href=\"https://imoveisfederais.planejamento.gov.br/spin-web/login\" target=\"_blank\" id=\"pesquisaImovel\" >\n            <div style=\"width: 100%; height: 87%; background-repeat: round;\" id=\"mapa-pesquisa-imovel\"\n                 ng-style=\"{\'background-image\':\'url(/assets/img/mapa.png)\' }\">\n\n                <i class=\"fa fa-search fa-5x\" aria-hidden=\"true\"></i>\n            </div>\n        </a>--></div><div layout=column style=\"align-items: flex-end\"><md-button class=color-pesq-imoveis ng-href=https://imoveisfederais.planejamento.gov.br/spin-web/login target=_blank><span>+ Clique Aqui</span><md-icon class=icon-forward-pesq-imoveis md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div></div>");
$templateCache.put("scripts-integrador/pages/informes/language/pt-BR.json","{ \"label-titulo\": \"Título:\", \"label-periodo\": \"Período de publicação\", \"label-data\": \"Data\", \"label-data-publicacao\": \"Data de Publicação\", \"label-texto\": \"Texto\", \"label-acoes\": \"Ações\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-novo\": \"Novo\", \"btn-gravar\": \"Gravar\", \"btn-pre-visualizar\": \"Pré Visualizar\", \"tooltip-limpar-campos\": \"Limpar campos da tela\", \"tooltip-pesquisar\": \"Pesquisar dados conforme filtro\", \"tooltip-novo\": \"Incluir novo registro\", \"label-imagem\": \"Imagem:\", \"label-selecionar-arquivo\": \"Selecionar arquivo\" }");
$templateCache.put("scripts-integrador/pages/informes/view/cadastroInformes.html","<md-content flex><h2 class=tituloFormulario>Cadastro de Informes</h2><md-content flex><form name=informeForm ng-submit=\"validarFormulario(informeForm) && enviarFormulario()\" novalidate><div layout=row layout-xs=column flex=30><label>{{\'label-data-publicacao\' | translate}}: &ensp;</label><label>{{informe.dataPublicacao ? informe.dataPublicacao : dataPublicacaoInforme | date : \'dd/MM/yyyy\'}}</label></div><div layout=row layout-xs=column style=\"margin-top: 1%\"><md-input-container class=md-block flex-gt-xs style=\"margin-bottom: 0px !important\"><label>{{\'label-titulo\' | translate}}</label><input ng-model=informe.titulo name=titulo type=text required><!-- <p ng-show=\"informeForm.titulo.$invalid && !informeForm.titulo.$pristine\" class=\"obrigatorio\">{{\'erro-campos-obrigatorio\' | translate}}</p> --></md-input-container></div><div layout=row layout-xs=column style=\"margin-top: 1%\"><md-input-container class=md-block flex=50 flex-gt-xs style=\"margin-bottom: 0px !important\"><label>{{\'label-imagem\' | translate}}</label><input ng-model=imagem.name name=imagem type=text disabled></md-input-container><md-container flex-gt-sm flex-offset=5><md-button class=md-raised ngf-select=uploadImagem($files) id=file name=file ngf-multiple=false accept=image/*>{{\'label-selecionar-arquivo\' | translate}}</md-button></md-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs style=\"margin-top: 0px !important\"><label>{{\'label-texto\' | translate}}*:</label></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs><text-angular ng-model=informe.corpo name=corpo required ta-toolbar-button-class=\"btn btn-default\" ta-toolbar-button-active-class=active></text-angular><!-- <p ng-show=\"informeForm.corpo.$invalid && !informeForm.corpo.$pristine\" class=\"obrigatorio\">{{\'erro-campos-obrigatorio\' | translate}}</p> --></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=limpar()><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate}}</md-button><md-button class=md-raised type=submit value=PRE_VISUALIZAR ng-click=\"acaoSubmit=PRE_VISUALIZAR\">{{\'btn-pre-visualizar\' | translate}}</md-button><md-button class=md-raised type=submit exibir-elemento-por-permissao=INTEGRADOR.INFORMES_CADASTRAR ng-click=\"acaoSubmit=SALVAR\">{{\'btn-gravar\' | translate}}</md-button></section></div><!--- fim dos botoes --></form></md-content></md-content>");
$templateCache.put("scripts-integrador/pages/informes/view/consultarInformes.html","<md-content flex><h2 class=tituloFormulario>Consulta de Informes</h2><md-content flex><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=70><label>{{\'label-titulo\' | translate}}</label><input ng-model=filtros.titulo type=text></md-input-container><md-input><h4 style=\"margin: 0; font-weight: normal\">{{\'label-periodo\' | translate}}</h4><md-datepicker ng-model=filtros.dataPublicacaoInicio md-open-on-focus></md-datepicker></md-input><md-input class=md-block style=\"padding-top: 11px; padding-left: 2px\"><label>a</label><md-datepicker ng-model=filtros.dataPublicacaoFim></md-datepicker></md-input></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=clearFilter()><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate}}</md-button><md-button class=md-raised exibir-elemento-por-permissao=INTEGRADOR.INFORMES_CONSULTAR ng-click=findInformesByFilter()><md-tooltip md-direction=bottom>{{\'tooltip-pesquisar\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate}}</md-button><md-button class=md-raised exibir-elemento-por-permissao=INTEGRADOR.INFORMES_CADASTRAR ng-click=\"redirecionaTela(\'cadastroInformes\')\"><md-tooltip md-direction=bottom>{{\'tooltip-novo\' | translate}}</md-tooltip>{{\'btn-novo\' | translate}}</md-button></section></div><!--- fim dos botoes --><div flex class=caixa-tabela-resultado><div layout=column ng-show=\"listaInformes.length > 0\"><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-data\' | translate}}</th><th md-column>{{\'label-titulo\' | translate}}</th><th md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaInformes\"><td md-cell><span ng-bind=item.dataPublicacao></span></td><td md-cell><span ng-bind=item.titulo></span></td><td md-cell><span layout=row layout-align=\"left center\"><a style=\"text-decoration: none\" ng-click=carregaInformeEdicao(item)><ng-md-icon exibir-elemento-por-permissao=INTEGRADOR.INFORMES_CADASTRAR class=hand icon=create style=fill:#788489 size=22></ng-md-icon></a><ng-md-icon class=hand icon=clear style=fill:#788489 size=22 exibir-elemento-por-permissao=INTEGRADOR.INFORMES.EXCLUIR ng-click=\"showConfirmExcluir($event, item.id, $index);\"><md-tooltip>Excluir Informe</md-tooltip></ng-md-icon></span></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listaInformes.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findAll md-page-select></md-table-pagination></md-conent></div></div></md-content></md-content>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/language/pt-BR.json","{ \"label-modulo\": \"Módulo\", \"label-instrumento-funcionalidade\": \"Instrumento/Funcionalidade\", \"label-competencia-autorizacao\": \"Competência para autorização\", \"label-fundamento-legal\": \"Fundamento Legal\", \"label-remover-fundamento-legal\": \"Remover Fundamento Legal\", \"label-confirmacao-remover-fundamento\":\"Este fundamento legal está vinculado a itens no checklist. Se for removido, os itens permanecerão no checklist desvinculados do fundamento. Deseja continuar?\", \"label-checklist-especifico\": \"Checklist Específico de Análise\", \"label-item-checklist-especifico\": \"Item do Checklist Específico de Análise\", \"label-adicionar\": \"Adicionar\", \"label-retornar\": \"Retornar\", \"label-gravar\": \"Gravar\", \"label-consultar\": \"Consultar\", \"label-inserir\": \"inserir\", \"label-acoes\": \"Ações\", \"label-confirmar\": \"Confirmar\", \"label-item\": \"Item\", \"label-justificativa\": \"Justificativa\", \"label-suspensao\": \"SUSPENÇÃO\", \"label-confirmar-edicao\":\"Confirmar Edição\", \"label-cancelar-edicao\":\"Cancelar Edição\", \"label-modo-consulta\":\"Modo Consulta\", \"label-modo-inclusao\":\"Modo Inclusão\", \"label-consulta-fundamento-legal\":\"Consultar Fundamento Legal\", \"label-consulta-checklist-especifico\":\"Consultar Checklist\", \"label-competencia\":\"Competência\", \"label-exibir-ato\":\"Exibir Ato Autorizativo\", \"label-titulo\": \"Título\", \"label-descricao\": \"Descrição\", \"label-responsavel\": \"Responsável\", \"tooltip-editar\": \"Editar\", \"tooltip-pesquisar\": \"Pesquisar\", \"tooltip-novo\": \"Novo\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-novo\": \"Novo\", \"btn-editar\": \"Editar\", \"btn-fundamento-legal\": \"Vincular fundamento legal\", \"btn-check-list\": \"Vincular check list\", \"btn-contrato\": \"Contrato\", \"btn-suspender\":\"Suspender Instrumento/Funcionalidade\", \"btn-confirmar\":\"Confirmar\", \"btn-cancelar\":\"Cancelar\", \"btn-vincular-ato-contrato\":\"Vincular Ato/Contrato\", \"btn-vincular-checklist-de-analise\":\"Vincular Checklist de análise\", \"btn-vincular-fundamento-legal\":\"Vincular Fundamento Legal\", \"msg-nenhum-registro-encontrado\": \"Nenhum registro encontrado\", \"msg-justificativa\": \"Campo Justificativa Obrigatório\", \"msg-instrumento-funcionalidade-suspendida\":\"Instrumento/Funcionalidade suspenso\", \"msg-instrumento-funcionalidade-n-suspensa\": \"Suspensão cancelada\" }");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/cadastroInstrumentoFuncionalidade.html","<div><form name=form><titulo prefixo-titulo=\"\'Cadastrar\'\"></titulo><div layout=row><md-input-container flex=50><label>{{\'label-modulo\' | translate}}:</label><md-select ng-model=instrumento.modulo ng-required=true ng-disabled=isEdicao ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=buscarFuncionalidadePorModulo() name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"modulo in modulos | orderBy:\'descricao\'\" ng-value=modulo>{{modulo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>{{\'label-instrumento-funcionalidade\' | translate}}:</label><md-select ng-model=instrumento.instrumentoFuncionalidade ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-instrumento-funcionalidade\' | translate}}\" ng-disabled=isEdicao><md-option ng-repeat=\"funcionalidade in funcionalidades | orderBy:\'descricao\'\" ng-value=funcionalidade>{{funcionalidade.descricao}}</md-option></md-select></md-input-container><!--<md-input-container flex=\"50\" ng-if=\"idEdicao\">\n        <label>{{\'label-instrumento-funcionalidade\' | translate}}:</label>\n        <input ng-model=\"instrumento.descricao\" disabled>\n      </md-input-container>--></div><div layout=row><md-input-container flex=100><label>{{\'label-titulo\' | translate}}</label><input name=\"{{\'label-titulo\' | translate}}\" ng-model=instrumento.titulo md-maxlength=100 maxlength=100 required></md-input-container></div><div layout=row><md-input-container flex=100 style=\"margin-top: 0\"><label>{{\'label-descricao\' | translate}}</label><input name=\"{{\'label-descricao\' | translate}}\" ng-model=instrumento.descricao md-maxlength=100 maxlength=100 required></md-input-container></div><div style=\"margin-top: 15%\"><div class=margin-top layout=row layout-align=\"end center\"><md-button class=md-raised ng-click=retornar()>{{\'label-retornar\' | translate}}</md-button><md-button class=md-raised ng-show=isEdicao ng-click=vincularFundamentoLegal()>{{\'btn-vincular-fundamento-legal\' | translate}}</md-button><md-button class=md-raised ng-show=isEdicao ng-click=\"\">{{\'btn-vincular-ato-contrato\' | translate}}</md-button><md-button class=md-raised ng-show=isEdicao ng-click=\"\">{{\'btn-vincular-checklist-de-analise\' | translate}}</md-button><md-button class=md-raised ng-click=salvarInstrumento()>{{\'label-gravar\' | translate}}</md-button></div></div></form></div>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/consultaInstrumentoFuncionalidade.html","<div><titulo prefixo-titulo=\"\'Consultar\'\"></titulo><div layout=row><md-input-container flex=50><label>{{\'label-modulo\' | translate}}</label><md-select ng-model=modulo ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=buscarFuncionalidadePorModulo() name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in modulos | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>{{\'label-instrumento-funcionalidade\' | translate}}:</label><md-select ng-model=instrumentoFuncionalidadeId ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-funcionalidade\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"funcionalidade in funcionalidades | orderBy:\'descricao\'\" ng-value={{funcionalidade}}>{{funcionalidade.descricao}}</md-option></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=limparPesquisa() exibir-elemento-por-permissao=\"DESTINACAO_CONSULTAR_INSTRUM_FUNC_C_DOMINIALIDADE,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_GESTAO_PRAIAS,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CESSAO_GRATUITA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CESSAO_ONEROSA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_DOACAO_TRANSF,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CUEM,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_OCUPACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_VENDA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CDRU,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_TR_TITULARIDADE,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_INSC_OCUPACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_AFORAMENTO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_LOCACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_ARRENDAMENTO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_UTILIZACAO_A_REGULARIZAR,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_USO_PROPRIO\"><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate }}</md-button><md-button class=md-raised ng-click=filterAll(instrumentoFuncionalidadeId.id) exibir-elemento-por-permissao=\"DESTINACAO_CONSULTAR_INSTRUM_FUNC_C_DOMINIALIDADE,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_GESTAO_PRAIAS,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CESSAO_GRATUITA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CESSAO_ONEROSA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_DOACAO_TRANSF,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CUEM,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_OCUPACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_VENDA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CDRU,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_TR_TITULARIDADE,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_INSC_OCUPACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_AFORAMENTO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_LOCACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_ARRENDAMENTO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_UTILIZACAO_A_REGULARIZAR,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_USO_PROPRIO\"><md-tooltip md-direction=bottom>{{\'tooltip-pesquisar\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate }}</md-button><md-button class=md-raised ng-click=cadastrarNovoInstrumento() exibir-elemento-por-permissao=\"DESTINACAO_MANTER_INSTRUM_FUNC_C_DOMINIALIDADE,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_GESTAO_PRAIAS,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_CESSAO_GRATUITA,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_CESSAO_ONEROSA,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_DOACAO_TRANSF,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_CUEM,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_OCUPACAO,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_VENDA,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_CDRU,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_TR_TITULARIDADE,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_INSC_OCUPACAO,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_AFORAMENTO,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_LOCACAO,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_ARRENDAMENTO,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_UTILIZACAO_A_REGULARIZAR,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_USO_PROPRIO\"><md-tooltip md-direction=bottom>{{\'tooltip-novo\' | translate}}</md-tooltip>{{\'btn-novo\' | translate }}</md-button></section></div><!--- fim dos botoes --><div flex style=\"margin-top: 3%\" class=switch ng-show=\"funcionalidadesList.length > 0\"><div layout=column><md-conent flex><md-table-container><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\" class=upload-head><th md-column class=texto-centro>Módulo</th><th md-column class=texto-centro>Instrumento / Funcionalidade</th><th md-column style=\"padding-left: 90px\">Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"funcionalidade in funcionalidadesList | orderBy: funcionalidade.modulo.descricao\"><td md-cell class=texto-centro-corpo><span ng-bind=funcionalidade.instrumentoFuncionalidade.modulo.descricao></span></td><td md-cell class=texto-centro-corpo><span ng-bind=funcionalidade.instrumentoFuncionalidade.descricao></span></td><td md-cell class=texto-centro-corpo style=\"position: relative\"><md-fab-speed-dial md-direction=left class=\"hand md-scale\" style=\"position: relative\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><div exibir-elemento-por-permissao=\"DESTINACAO_MANTER_INSTRUM_FUNC_C_DOMINIALIDADE,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_GESTAO_PRAIAS,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_CESSAO_GRATUITA,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_CESSAO_ONEROSA,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_DOACAO_TRANSF,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_CUEM,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_OCUPACAO,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_VENDA,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_CDRU,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_TR_TITULARIDADE,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_INSC_OCUPACAO,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_AFORAMENTO,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_LOCACAO,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_ARRENDAMENTO,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_UTILIZACAO_A_REGULARIZAR,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_USO_PROPRIO\"><ng-md-icon class=icon-subacao icon=edit ng-click=editarFuncionalidade(funcionalidade)><md-tooltip>{{\"btn-editar\" | translate}}</md-tooltip></ng-md-icon></div><ng-md-icon class=icon-subacao ng-click=vincularFundamentoLegal(funcionalidade) icon=gavel><md-tooltip>{{\"btn-fundamento-legal\" | translate}}</md-tooltip></ng-md-icon><ng-md-icon class=icon-subacao icon=playlist_add_check><md-tooltip>{{\"btn-check-list\" | translate}}</md-tooltip></ng-md-icon><div class=icone-contrato><md-tooltip>{{\"btn-contrato\" | translate}}</md-tooltip><img src=../../../../assets/img/contrato_grey.png></div><div exibir-elemento-por-permissao=\"DESTINACAO_SUSPENDER_INSTRUM_FUNC_C_DOMINIALIDADE,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_GESTAO_PRAIAS,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_CESSAO_GRATUITA,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_CESSAO_ONEROSA,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_DOACAO_TRANSF,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_CUEM,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_OCUPACAO,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_VENDA,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_CDRU,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_TR_TITULARIDADE,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_INSC_OCUPACAO,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_AFORAMENTO,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_LOCACAO,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_ARRENDAMENTO,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_UTILIZACAO_A_REGULARIZAR,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_USO_PROPRIO\"><md-tooltip>{{\"btn-suspender\" | translate}}</md-tooltip><md-switch ng-model=funcionalidade.suspenso ng-change=suspenderInstrumento(funcionalidade) exibir-elemento-por-permissao=INTEGRADOR.INSTR-FUNC.SUSPENDER class=\"md-primary switch\" style=\"margin: -4px 0px 0px 0px\"></md-switch></div></md-fab-actions></md-fab-speed-dial></td></tr></tbody></table></md-table-container><md-table-pagination style=\"border: 1px solid #cccccc; margin:auto; height: 63px\" ng-show=\"funcionalidadesList.length > 0\" md-limit=paginacao.limit md-limit-options=paginacao.limitsPage md-page=paginacao.page md-total={{totalItems}} md-on-paginate=filterAll md-page-select></md-table-pagination></md-conent></div></div></div>");
$templateCache.put("scripts-integrador/pages/orientacao-tecnica/language/pt-BR.json","{ \"label-selecionar-arquivo\": \"Selecionar arquivo\", \"button-editar\": \"Editar\", \"button-deletar\": \"Deletar\", \"button-visualizar\": \"Visualizar\" }");
$templateCache.put("scripts-integrador/pages/orientacao-tecnica/view/orientacaoTecnica.html","<h1>Cadastro de Orientação Técnica</h1><form name=orientacaoTecnicaForm novalidate layout=column ng-submit=salvar() class=custom-style><md-input-container><label>Nome da Orientação Técnica:</label><input required ng-model=orientacaoTecnica.titulo maxlength=100 md-maxlength=100 ng-disabled=consultar></md-input-container><div layout=row flex=100><md-input-container flex=50><label>Área:</label><md-select ng-model=orientacaoTecnica.areaOrientacaoTecnica required ng-disabled=consultar><md-option ng-repeat=\"area in areasOrientacaoTecnica\" ng-selected=\"area.id == orientacaoTecnica.areaOrientacaoTecnica.id\" ng-value=area>{{area.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>Assunto:</label><md-select ng-model=orientacaoTecnica.assuntoOrientacaoTecnica ng-disabled=consultar><md-option ng-repeat=\"assunto in assuntosOrientacaoTecnica\" ng-selected=\"assunto.id == orientacaoTecnica.assuntoOrientacaoTecnica.id\" ng-value=assunto>{{assunto.descricao}}</md-option></md-select></md-input-container></div><div><h3 class=green-text>Links de Referência</h3><md-table-container class=\"md-block margin-top-2-em\" flex-gt-xs flex=100><table md-table><thead md-head><tr md-row><th md-column>Tipo do Ato/Orientação Técnica</th><th md-column>Nº do Ato/Orientação Técnica</th><th md-column>Link</th><th md-column style=\"text-align: center !important\">Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"link in orientacaoTecnica.listLinkOrientacaoTecnica track by $index\" ng-if=!link.isExcluido><td md-cell>{{link.tipoOrientacaoTecnica.descricao}}</td><td md-cell>{{link.numeroOrientacaoTecnica}}</td><td md-cell>{{link.descricaoLink}}</td><td md-cell style=\"position: relative; display: block\"><md-fab-speed-dial md-direction=left style=\"margin-right: 50%; margin-top: -3px; transform: translateX(20%)\" class=\"hand md-scale alinhar-md-fab-speed-dial\" layout-align=\"end end\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><a href=\"\" ng-click=abrirModalLink($index) ng-if=!consultar><ng-md-icon class=icon-subacao icon=create style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-editar\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=deletarLink($index) ng-if=!consultar><ng-md-icon class=icon-subacao icon=delete style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-deletar\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=\"abrirModalLink($index, true)\"><ng-md-icon class=icon-subacao icon=search style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-visualizar\' | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></td></tr><tr ng-if=\"!orientacaoTecnica.listLinkOrientacaoTecnica || orientacaoTecnica.listLinkOrientacaoTecnica.length == 0\"><td>Nenhum item...</td></tr></tbody></table></md-table-container><div layout=row layout-align=end><md-button class=md-raised ng-click=abrirModalLink() ng-if=!consultar>Adicionar Link</md-button></div></div><div><h3 class=green-text>Procedimentos Operacionais Padrão</h3><md-table-container class=\"md-block margin-top-2-em\" flex-gt-xs flex=100><table md-table><thead md-head><tr md-row><th md-column>Título</th><th md-column>Finalidade</th><th md-column>Procedimento</th><th md-column style=\"text-align: center !important\">Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"pop in orientacaoTecnica.listProcedimentoOperacionalPadrao track by $index\" ng-if=!pop.isExcluido><td md-cell>{{pop.titulo}}</td><td md-cell>{{pop.finalidade}}</td><td md-cell class=mais-menos-wrapper><input type=checkbox id={{$index}}><div ng-bind-html=pop.procedimento></div><label for={{$index}}></label></td><td md-cell style=\"position: relative; display: block\"><md-fab-speed-dial md-direction=left style=\"margin-right: 50%; margin-top: -3px; transform: translateX(20%)\" class=\"hand md-scale alinhar-md-fab-speed-dial\" layout-align=\"end end\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><a href=\"\" ng-click=editarPop($index) ng-if=!consultar><ng-md-icon class=icon-subacao icon=create style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-editar\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=deletarPop($index) ng-if=!consultar><ng-md-icon class=icon-subacao icon=delete style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-deletar\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=visualizarPop($index)><ng-md-icon class=icon-subacao icon=search style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-visualizar\' | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></td></tr><tr ng-if=\"!orientacaoTecnica.listProcedimentoOperacionalPadrao || orientacaoTecnica.listProcedimentoOperacionalPadrao.length == 0\"><td>Nenhum item...</td></tr></tbody></table></md-table-container><div layout=row layout-align=end><md-button class=md-raised ng-click=inserirPop() ng-if=!consultar>Adicionar POP</md-button></div></div><md-input-container><label>Resumo da publicação/alteração:</label><textarea ng-model=orientacaoTecnica.resumoPublicacao maxlength=600 md-maxlength=600 ng-disabled=consultar>\n    </textarea></md-input-container><div layout=row layout-align=end><md-button class=md-raised ng-click=voltar()>Voltar</md-button><input class=\"md-raised md-button md-ink-ripple\" type=submit ng-click=setRascunho() value=\"Salvar como rascunho\" ng-if=!consultar> <input class=\"md-raised md-button md-ink-ripple\" type=submit value=Concluir ng-if=!consultar></div></form>");
$templateCache.put("scripts-integrador/pages/procedimento-operacional-padrao/language/pt-BR.json","{ \"label-selecionar-arquivo\": \"Selecionar arquivo\" }");
$templateCache.put("scripts-integrador/pages/procedimento-operacional-padrao/view/procedimentoOperacionalPadrao.html","<h1 class=green-text>Cadastro de Procedimento Operacional Padrão (POP)</h1><form name=formularioPop ng-submit=salvar() novalidate layout=column class=custom-style><md-input-container flex=100><label>Título</label><input ng-model=pop.titulo required maxlength=100 md-maxlength=100 ng-disabled=consultar></md-input-container><md-input-container flex=100><label>Finalidade</label><input ng-model=pop.finalidade required maxlength=200 md-maxlength=200 ng-disabled=consultar></md-input-container><label>Procedimento*</label><md-input-container flex=100 class=textangular><div text-angular ng-model=pop.procedimento name=descricao ta-max-text=5000 ng-if=!consultar ta-toolbar-button-class=\"btn btn-default\" ta-toolbar-button-active-class=active name=procedimento></div><div class=consultarTextAngular ng-if=consultar ng-bind-html=pop.procedimento></div></md-input-container><h3 class=green-text>Fluxo</h3><md-container flex-gt-sm><ul><li ng-repeat=\"arquivo in pop.listArquivoFluxo\">{{arquivo.nome}}</li></ul><md-button class=md-raised ngf-select=uploadArquivoFluxo($files) id=file name=file ngf-multiple=false ng-disabled=consultar>{{\'label-selecionar-arquivo\' | translate}}</md-button></md-container><h3 class=green-text>Arquivos auxiliares</h3><md-container flex-gt-sm><ul><li ng-repeat=\"arquivo in pop.listArquivoAuxiliar\">{{arquivo.nome}}</li></ul><md-button class=md-raised ngf-select=uploadArquivoAuxiliar($files) id=file2 name=file2 ngf-multiple=false ng-disabled=consultar>{{\'label-selecionar-arquivo\' | translate}}</md-button></md-container><div layout=row layout-align=end><md-button class=md-raised ng-click=voltar()>Voltar</md-button><input class=\"md-raised md-button md-ink-ripple\" type=submit ng-click=setRascunho() ng-disabled=!pop.titulo value=\"Salvar como rascunho\" ng-if=!consultar> <input class=\"md-raised md-button md-ink-ripple\" type=submit value=Concluir ng-if=!consultar></div></form>");
$templateCache.put("scripts-integrador/pages/siapa/view/siapa.tmpl.html","<div ng-if=apresentarFrame()><iframe iframe-set-dimensions-onload src={{url}}></iframe></div>");
$templateCache.put("scripts-integrador/pages/textos-ajuda/language/pt-BR.json","{ \"label-modulo\": \"Módulo\", \"label-funcionalidade-componente\": \"Funcionalidade/Componente\", \"label-data-ultima-atualizacao\": \"Data da Última Atualização\", \"label-campo-formulario\": \"Campo do Formulário\", \"label-texto-ajuda\": \"Texto de Ajuda\", \"btn-salvar\": \"Salvar\" }");
$templateCache.put("scripts-integrador/pages/textos-ajuda/view/cadastroTextosAjuda.html","<md-content flex><h2 ng-show=!textosAjudaCtrl.isEditar class=tituloFormulario>Cadastro Textos de Ajuda</h2><h2 ng-show=textosAjudaCtrl.isEditar class=tituloFormulario>Editar Textos de Ajuda</h2><ng-form name=cadastroTextosAjudaForm novalidate><div layout=row layout-xs=column><md-input-container flex=50><label>{{\'label-modulo\' | translate}}</label><md-select ng-disabled=textosAjudaCtrl.isEditar required ng-model=textosAjudaCtrl.query.modulo ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=textosAjudaCtrl.buscarFuncionalidadePorModuloSemAjuda() name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"modulo in textosAjudaCtrl.modulos | orderBy:\'descricao\'\" ng-value={{modulo}}>{{modulo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>{{\'label-funcionalidade-componente\' | translate}}</label><md-select ng-disabled=textosAjudaCtrl.isEditar required ng-model=textosAjudaCtrl.query.funcionalidade ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-funcionalidade-componente\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"funcionalidade in textosAjudaCtrl.funcionalidades | orderBy:\'id\'\" ng-value={{funcionalidade}}>{{funcionalidade.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column layout-align=end ng-show=!textosAjudaCtrl.isEditar><md-button ng-click=textosAjudaCtrl.pesquisarCamposAjuda() class=md-raised>{{\'btn-pesquisar\' | translate}}</md-button></div><div ng-show=\"textosAjudaCtrl.camposAjuda.length > 0\"><div layout=row layout-xs=column><h2 flex=50 class=titulo-secundario-formulario>{{\'label-campo-formulario\' | translate}}</h2><h2 flex=50 class=titulo-secundario-formulario>{{\'label-texto-ajuda\' | translate}}</h2></div><div ng-repeat=\"campoAjuda in textosAjudaCtrl.camposAjuda | orderBy:\'id\'\"><div layout=row layout-xs=column><md-input-container flex=50><span ng-bind=campoAjuda.nome></span></md-input-container><md-input-container flex=50><textarea ng-model=campoAjuda.ajuda name=textoAjuda ng-maxlength=500 maxlength=500></textarea><span class=textAreaCount>{{campoAjuda.ajuda.length}} / 500</span></md-input-container></div><div ng-repeat=\"subCampoAjuda in campoAjuda.nos\"><div layout=row layout-xs=column><md-input-container flex=50><span class=sub-campo-ajuda ng-bind=subCampoAjuda.nome></span></md-input-container><md-input-container flex=50><textarea ng-model=subCampoAjuda.ajuda name=textoAjuda ng-maxlength=500 maxlength=500></textarea><span class=textAreaCount>{{subCampoAjuda.ajuda.length}} / 500</span></md-input-container></div></div><div class=separador></div></div></div><div layout=row layout-xs=column layout-align=\"end right\"><md-button ng-click=\"textosAjudaCtrl.redirecionarTela(\'INTEGRADOR.TEXTOS_AJUDA\')\" class=md-raised>{{\'btn-fechar\' | translate}}</md-button><md-button ng-show=\"textosAjudaCtrl.camposAjuda.length > 0\" ng-click=textosAjudaCtrl.salvarTextosAjuda(cadastroTextosAjudaForm) class=md-raised>{{\'btn-salvar\' | translate}}</md-button></div></ng-form></md-content>");
$templateCache.put("scripts-integrador/pages/textos-ajuda/view/manutencaoTextosAjuda.html","<md-content flex><h2 class=tituloFormulario>Manutenção de Textos de Ajuda</h2><md-content flex><div layout=row layout-xs=column><md-input-container flex=50><label>{{\'label-modulo\' | translate}}</label><md-select ng-model=textosAjudaCtrl.query.modulo ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=textosAjudaCtrl.buscarFuncionalidadePorModulo() name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"modulo in textosAjudaCtrl.modulos | orderBy:\'descricao\'\" ng-value={{modulo}}>{{modulo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>{{\'label-funcionalidade-componente\' | translate}}</label><md-select ng-model=textosAjudaCtrl.query.funcionalidade ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-funcionalidade-componente\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"funcionalidade in textosAjudaCtrl.funcionalidades | orderBy:\'id\'\" ng-value={{funcionalidade}}>{{funcionalidade.nome}}</md-option></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=textosAjudaCtrl.limpar()><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate}}</md-button><md-button class=md-raised ng-click=textosAjudaCtrl.pesquisar()><md-tooltip md-direction=bottom>{{\'tooltip-pesquisar\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate}}</md-button><md-button class=md-raised ng-click=\"textosAjudaCtrl.redirecionarTela(\'INTEGRADOR.CADASTRO_TEXTOS_AJUDA\')\">{{\'btn-novo\' | translate}}</md-button></section></div><!--- fim dos botoes --><div flex><div layout=column ng-show=\"textosAjudaCtrl.listaFuncionalidades.length > 0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-modulo\' | translate}}</th><th md-column>{{\'label-funcionalidade-componente\' | translate}}</th><th md-column>{{\'label-data-ultima-atualizacao\' | translate}}</th><th md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in textosAjudaCtrl.listaFuncionalidades\"><td md-cell><span ng-bind=item.modulo.descricao></span></td><td md-cell><span ng-bind=item.nome></span></td><td md-cell><span ng-bind=\"item.dataUltimaAtualizacao | date : \'dd/MM/yyyy\':\'UTC\'\"></span></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=textosAjudaCtrl.irParaEdicao(item.id) class=\"md-fab md-mini\" aria-label=\"Editar Campo de Ajuda\" style=\"background-color: #f7f7f7\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Campo de Ajuda</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"textosAjudaCtrl.listaFuncionalidades.length > 0\" md-limit=textosAjudaCtrl.query.maximoPorPagina md-limit-options=\"[5, 10, 15]\" md-page=textosAjudaCtrl.query.numeroPagina md-total={{textosAjudaCtrl.query.totalElements}} md-on-paginate=textosAjudaCtrl.pesquisar md-page-select></md-table-pagination></md-content></div></div></md-content></md-content>");
$templateCache.put("scripts-integrador/app/directives/fundamento-legal/template/incluirUtilizacao.html","<div layout=row flex><h3>{{\'label-utilizacao\' | translate}}</h3></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=40 class=md-block><label>{{\'label-tipo-uso-permitido\'| translate}}</label><md-select multiple md-on-open=buscarEspecificacoes() ng-change=buscarEspecificacao(); ng-model=utilizacao.tipoUsoPermitido ng-disabled=\"bloquear || isDetalhar\"><md-checkbox class=md-primary ng-model=todosTipos ng-if=\"listaTipoUsoPermitido.length > 0\" ng-change=selecionarTodosTipo(todosTipos)>{{\'label-todos\' | translate}}</md-checkbox><md-option ng-repeat=\"item in listaTipoUsoPermitido\" ng-value=item ng-if=validarExibicaoTipoPermitido(item)>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container style=\"margin-top: 0px\" flex=40 class=md-block><label>{{\'label-especificacao\'| translate}}</label><md-select multiple ng-disabled=\"bloquear || isDetalhar\" ng-model=utilizacao.especificacoes><md-checkbox class=md-primary ng-if=\"listaEspecificacao.length > 0\" ng-model=tipoEspecificacao ng-change=selecionarTodosEspecificacao(tipoEspecificacao)>{{\'label-todos\' | translate}}</md-checkbox><md-option ng-repeat=\"itemEspecificacao in listaEspecificacao \" ng-value=itemEspecificacao ng-if=validarExibicaoEspecificacao(itemEspecificacao)>{{itemEspecificacao.descricao}}</md-option></md-select></md-input-container><md-button class=md-raised ng-disabled=\"bloquear || isDetalhar\" flex=20 ng-click=adicionarListaUtilizacoes()>{{\'label-adicionar\'| translate}}</md-button></div><md-content ng-show=\"utilizacoes.length > 0\"><md-table-container><table md-table class=md-border-table><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-utilizacao\'|translate}}</th><th md-column>{{\'label-especificacao\' | translate}}</th><th md-column ng-show=\"!bloquear && !isDetalhar\">{{\'label-acao\'|translate}}</th></tr></thead><tbody md-body><tr ng-repeat-start=\"itemUsoEspecificacao in utilizacoes\"><td rowspan={{itemUsoEspecificacao.especificacoes.length}}><label>{{itemUsoEspecificacao.tipoUsoPermitido.descricao}}</label></td><td>{{itemUsoEspecificacao.especificacoes[0].descricao}}</td><td ng-show=\"!bloquear && !isDetalhar\"><ng-md-icon class=icon-subacao icon=delete ng-click=\"removerUsoEspecificacao($event, 0, itemUsoEspecificacao)\"><md-tooltip>{{\"label-excluir\" | translate}}</md-tooltip></ng-md-icon></td></tr><tr ng-repeat=\"esp in itemUsoEspecificacao.especificacoes\" ng-if=!$first ng-repeat-end><td>{{esp.descricao}}</td><td ng-show=\"!bloquear && !isDetalhar\"><ng-md-icon class=icon-subacao icon=delete ng-click=\"removerUsoEspecificacao($event, $index, itemUsoEspecificacao)\"><md-tooltip>{{\"label-excluir\" | translate}}</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><div class=paginationUtilizacao></div></md-content>");
$templateCache.put("scripts-integrador/app/directives/secoes/templates/adicionarClausulas.html","<md-content flex><div layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised style=\"margin-left: 0\" ng-click=abrir($event)>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-if=\"clausulas.length > 0\" class=top-1><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-clausulas\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr ng-repeat=\"item in clausulas | orderBy:\'ordem\'\" md-row md-select=item md-select-id=id md-auto-select><td md-cell class=texto-centro-corpo>{{item.descricao}}</td><td md-cell class=texto-centro-corpo><a href=\"\" ng-click=abrir($event) style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22><md-tooltip>{{labelEditar}}</md-tooltip></ng-md-icon></a><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"Excluir documento\" ng-click=\"remover($event, $index);\"><md-tooltip>{{labelExcluir}}</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><!-- <md-table-pagination ng-show=\"listaDocumento.length > 0\"\n                                 md-limit=\"queryDocumento.limit\" md-limit-options=\"queryDocumento.limitsPage\"\n                                 md-page=\"queryDocumento.page\" md-total=\"{{listaDocumento.length}}\"\n                                 md-page-select=\"true\">\n            </md-table-pagination> --></div></div></md-content>");
$templateCache.put("scripts-integrador/app/directives/secoes/templates/adicionarEditorHtml.html","<md-content flex><div layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=abrir() style=\"margin-left: 0\" ng-if=!model.descricao>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-if=model class=top-1><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{config.titulo}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select><td md-cell class=texto-centro-corpo><label ng-bind-html=model.descricao></label></td><td md-cell class=texto-centro-corpo><a href=\"\" ng-click=editar($event) style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22><md-tooltip>{{labelEditar}}</md-tooltip></ng-md-icon></a><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'excluir-preambulo\' | translate}}\" ng-click=remover($event);><md-tooltip>{{labelExcluir}}</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container></div></div></md-content>");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/partials/modalConfigurarComponentes.html","<md-dialog id=su-integrador flex=60 ng-cloak aria-label=\"{{\'label-checklist-configurar-componetes\' | translate | uppercase}}\"><form name=formularioComponetes novalidate><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-checklist-configurar-componetes\' | translate | uppercase}}</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro></th><th md-column class=texto-centro>{{\'label-informacoes-compor-modelo\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"componentes in dictConfigurarComponente\"><td md-cell class=\"celula tamanho-setas\" style=\"border-top: 0% !important; height: 0% !important\"><div layout=column flex=10><i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index-1, dictConfigurarComponente)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index+1, dictConfigurarComponente)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div ng-if=\"item.hierarquia == \'PRIMARIO\'\" ng-repeat=\"item in componentes.item\"><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox><input ng-model=item.descricao ng-maxlength=40 maxlength=40 class=pai capitalize> <span ng-show=\"mostrarVerCampos(componentes.item) && !componentes.verCampos\" class=\"margem-verCampos hand\" ng-click=\"componentes.verCampos = !componentes.verCampos\">{{\'ver-campos\' | translate}} </span><span ng-show=\"mostrarVerCampos(componentes.item) && componentes.verCampos\" class=\"margem-verCampos hand\" ng-click=\"componentes.verCampos = !componentes.verCampos\">{{\'ocultar-campos\' | translate}}</span></div><div ng-show=componentes.verCampos class=alinhar-checkbox ng-if=\"item.hierarquia == \'SECUNDARIO\'\" ng-repeat=\"item in componentes.item\"><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox><input ng-model=item.descricao ng-maxlength=40 maxlength=40 class=filho capitalize></div></td></tr></tbody></table></md-table-container></md-content></div><div layout=row layout-sm=column layout-align=\"end center\" class=top-2><md-button class=md-raised ng-click=fechar()>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised ng-click=confirmar()>{{\'btn-confirmar\' | translate}}</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/partials/modalExclusaoModelo.html","<md-dialog id=su-integrador aria-label=Suspender flex=60 ng-cloak><form name=excluirModelo><md-toolbar><div class=\"md-toolbar-tools text-center\"><h2>{{REMOVER}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row flex=100 layout-margin><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}*</label><textarea ng-model=justificativaExclusao required name=justificativa maxlength=500 md-maxlength=500></textarea></md-input-container></div><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=confirmar() class=md-raised>Salvar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/partial/cancelarFundamentoLegal.html","<div style=\"margin: 20px\"><form name=formCancelar><h2 class=tituloFormulario>{{\'label-cancelar-fundamento-legal\' | translate}}</h2><div layout=row><div layout=column flex=50><label>{{\'label-responsavel\' | translate}}:</label><b><p>{{responsavel.nome}} / {{responsavel.cpf | cpfcnpj}}</p></b></div><div layout=column flex=50><label>{{\'label-data\' | translate}}:</label><b><p>{{cancelamento.dataSuspensao | date: \'dd/MM/yyyy\'}}</p></b></div></div><div layout=row flex=100><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=cancelamento.justificativa required name=\"{{\'label-justificativa\' | translate}}\" maxlength=500 md-maxlength=500></textarea></md-input-container></div><div style=\"margin-top: 20%\"><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=fechar() class=md-raised>{{\'btn-fechar\' | translate}}</md-button><md-button ng-click=confirmar() class=md-raised>{{\'btn-confirmar\' | translate}}</md-button></div></div></form></div>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/partial/configuracoesRegiaoNatureza.html","<md-table-container><table md-table class=md-border-table><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-regioes\'|translate}}</th><th md-column>{{\'label-natureza\' | translate}}</th><th md-column>{{\'label-competencia\' | translate}}</th><th md-column>{{\'label-minimo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\' :\'label-valor-rs\'| translate}}</th><th md-column>{{\'label-maximo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\': \'label-valor-rs\'|translate}}</th></tr></thead><tbody md-body><!-- NORTE URBANO --><tr class=md-border-table><td rowspan=6 class=md-border-table><label>{{configuracoesRegiaoNatureza[0].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[0].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[0].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[0].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[0].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[0].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[0].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[0].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[0].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[0].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[0].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar) \" ng-model=configuracoesRegiaoNatureza[0].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[0].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[0].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[0].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[0].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORTE URBANO --><!-- NORTE RURAL --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[1].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[1].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[1].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[1].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[1].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[1].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[1].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[1].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[1].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[1].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[1].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[1].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[1].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[1].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[1].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORTE RURAL --><!-- NORDESTE URBANA --><tr class=md-border-table><td rowspan=6 class=md-border-table><label>{{configuracoesRegiaoNatureza[2].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[2].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[2].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[2].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[2].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[2].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[2].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[2].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[2].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[2].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[2].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[2].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[2].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[2].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[2].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[2].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORDESTE URBANA --><!-- NORDESTE RURAL --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[3].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[3].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[3].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[3].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[3].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[3].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[3].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[3].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[3].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[3].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[3].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[3].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[3].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[3].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[3].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORDESTE RURAL --><!-- CENTROOESTE URBANO --><tr class=md-border-table><td rowspan=6 class=md-border-table><label>{{configuracoesRegiaoNatureza[4].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[4].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[4].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[4].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[4].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[4].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[4].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[4].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[4].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[4].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[4].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[4].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[4].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[4].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[4].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[4].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- CENTROOESTE URBANO --><!-- CENTROOESTE RURAL --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[5].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[5].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[5].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[5].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[5].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[5].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[5].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[5].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[5].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[5].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[5].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[5].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[5].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[5].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[5].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- CENTROOESTE RURAL --><!-- SUDESTE URBANO --><tr class=md-border-table><td rowspan=6 class=md-border-table><label>{{configuracoesRegiaoNatureza[6].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[6].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[6].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[6].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[6].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[6].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[6].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[6].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[6].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[6].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[6].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[6].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[6].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[6].minimoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[6].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[6].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUDESTE URBANO --><!-- SUDESTE RURAL --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[7].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[7].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[7].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[7].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[7].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[7].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[7].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[7].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[7].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[7].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[7].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[7].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[7].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[7].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[7].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUDESTE RURAL --><!-- SUL URBANO --><tr class=md-border-table><td rowspan=6 class=md-border-table><label>{{configuracoesRegiaoNatureza[8].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[8].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[8].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[8].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[8].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[8].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[8].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[8].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[8].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[8].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[8].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[8].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[8].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[8].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[8].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[8].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUL URBANO --><!-- SUL RURAL --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[9].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[9].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[9].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[9].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[9].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[9].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[9].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[9].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[9].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[9].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[9].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[9].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[9].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[9].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[9].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUL RURAL --></tbody></table></md-table-container>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/partial/configuracoesRegiaoTodas.html","<md-table-container><table md-table class=md-border-table><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-regioes\'|translate}}</th><th md-column>{{\'label-natureza\' | translate}}</th><th md-column>{{\'label-competencia\' | translate}}</th><th md-column>{{\'label-minimo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\' :\'label-valor-rs\'| translate}}</th><th md-column>{{\'label-maximo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\': \'label-valor-rs\'|translate}}</th></tr></thead><tbody md-body><!-- NORTE TODAS --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[0].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[0].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[0].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoTodas[0].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoTodas[0].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[0].maximoSuperintendente ng-change=\"configuracoesRegiaoTodas[0].minimoSecretario = incrementarAreaValor(configuracoesRegiaoTodas[0].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[0].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoTodas[0].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoTodas[0].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[0].maximoSecretario ng-change=\"configuracoesRegiaoTodas[0].minimoMinistro = incrementarAreaValor(configuracoesRegiaoTodas[0].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[0].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoTodas[0].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORTE TODAS --><!-- NORDESTE TODAS --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[1].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[1].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[1].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoTodas[1].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoTodas[1].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[1].maximoSuperintendente ng-change=\"configuracoesRegiaoTodas[1].minimoSecretario = incrementarAreaValor(configuracoesRegiaoTodas[1].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[1].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoTodas[1].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoTodas[1].isSecretario === false ||  fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[1].maximoSecretario ng-change=\"configuracoesRegiaoTodas[1].minimoMinistro = incrementarAreaValor(configuracoesRegiaoTodas[1].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[1].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoTodas[1].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORDESTE TODAS --><!-- CENTROOESTE TODAS --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[2].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[2].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[2].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoTodas[2].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoTodas[2].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[2].maximoSuperintendente ng-change=\"configuracoesRegiaoTodas[2].minimoSecretario = incrementarAreaValor(configuracoesRegiaoTodas[2].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[2].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoTodas[2].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoTodas[2].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[2].maximoSecretario ng-change=\"configuracoesRegiaoTodas[2].minimoMinistro = incrementarAreaValor(configuracoesRegiaoTodas[2].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[2].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoTodas[2].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- CENTROOESTE TODAS --><!-- SUDESTE TODAS --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[3].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[3].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[3].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoTodas[3].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoTodas[3].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[3].maximoSuperintendente ng-change=\"configuracoesRegiaoTodas[3].minimoSecretario = incrementarAreaValor(configuracoesRegiaoTodas[3].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[3].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoTodas[3].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoTodas[3].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[3].maximoSecretario ng-change=\"configuracoesRegiaoTodas[3].minimoMinistro = incrementarAreaValor(configuracoesRegiaoTodas[3].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[3].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoTodas[3].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUDESTE TODAS --><!-- SUL TODAS --><tr class=md-border-table><td rowspan=3 style=\"border: 1px solid #cccccc\"><label>{{configuracoesRegiaoTodas[4].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[4].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[4].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoTodas[4].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoTodas[4].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[4].maximoSuperintendente ng-change=\"configuracoesRegiaoTodas[4].minimoSecretario = incrementarAreaValor(configuracoesRegiaoTodas[4].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[4].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoTodas[4].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoTodas[4].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[4].maximoSecretario ng-change=\"configuracoesRegiaoTodas[4].minimoMinistro = incrementarAreaValor(configuracoesRegiaoTodas[4].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[4].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoTodas[4].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUL TODAS --></tbody></table></md-table-container>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/partial/configuracoesTodasNatureza.html","<md-table-container><table md-table class=md-border-table><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-regioes\'|translate}}</th><th md-column>{{\'label-natureza\' | translate}}</th><th md-column>{{\'label-competencia\' | translate}}</th><th md-column>{{\'label-minimo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\' :\'label-valor-rs\'| translate}}</th><th md-column>{{\'label-maximo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\': \'label-valor-rs\'|translate}}</th></tr></thead><tbody md-body class=md-border-table><tr><td rowspan=6 class=md-border-table><label>{{configuracoesTodasNatureza[0].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesTodasNatureza[0].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[0].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesTodasNatureza[0].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesTodasNatureza[0].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesTodasNatureza[0].maximoSuperintendente ng-change=\"configuracoesTodasNatureza[0].minimoSecretario = incrementarAreaValor(configuracoesTodasNatureza[0].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[0].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesTodasNatureza[0].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesTodasNatureza[0].isSecretario === false ||fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesTodasNatureza[0].maximoSecretario ng-change=\"configuracoesTodasNatureza[0].minimoMinistro = incrementarAreaValor(configuracoesTodasNatureza[0].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[0].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesTodasNatureza[0].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><tr class=md-border-table><td rowspan=3 style=\"border: 1px solid #cccccc\"><label>{{configuracoesTodasNatureza[1].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[1].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesTodasNatureza[1].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesTodasNatureza[1].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesTodasNatureza[1].maximoSuperintendente ng-change=\"configuracoesTodasNatureza[1].minimoSecretario = incrementarAreaValor(configuracoesTodasNatureza[1].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr style=\"border: 2px solid #cccccc\"><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[1].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesTodasNatureza[1].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesTodasNatureza[1].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesTodasNatureza[1].maximoSecretario ng-change=\"configuracoesTodasNatureza[1].minimoMinistro = incrementarAreaValor(configuracoesTodasNatureza[1].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr style=\"border: 1px solid #cccccc\"><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[1].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesTodasNatureza[1].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/partial/configuracoesTodasTodas.html","<md-table-container><table md-table class=md-border-table><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-regioes\'|translate}}</th><th md-column>{{\'label-natureza\' | translate}}</th><th md-column>{{\'label-competencia\' | translate}}</th><th md-column>{{\'label-minimo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\' :\'label-valor-rs\'| translate}}</th><th md-column>{{\'label-maximo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\': \'label-valor-rs\'|translate}}</th></tr></thead><tbody md-body><tr><td rowspan=4 class=md-border-table><label>{{configuracoesTodasTodas[0].regiaoLabel}}</label></td><td rowspan=4 class=md-border-table><label>{{configuracoesTodasTodas[0].naturezaLabel}}</label></td></tr><tr ng-if=\"fundamentoLegal.isSuperintendente === true \" class=\"md-border-table md-config-table\"><td class=md-border-table><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" ng-model=configuracoesTodasTodas[0].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td class=texto-centro><label>{{configuracoesTodasTodas[0].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table><input ng-disabled=\"configuracoesTodasTodas[0].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesTodasTodas[0].maximoSuperintendente ng-change=\"configuracoesTodasTodas[0].minimoSecretario = incrementarAreaValor(configuracoesTodasTodas[0].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr ng-if=\"fundamentoLegal.isSecretario === true\" class=md-border-table><td class=md-border-table><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" ng-model=configuracoesTodasTodas[0].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td><label>{{configuracoesTodasTodas[0].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table><input ng-disabled=\"(configuracoesTodasTodas[0].isSecretario === false ||\n               fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesTodasTodas[0].maximoSecretario ng-change=\"configuracoesTodasTodas[0].minimoMinistro = incrementarAreaValor(configuracoesTodasTodas[0].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr ng-if=\"fundamentoLegal.isMinistro=== true\" class=md-border-table><td class=md-border-table><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" ng-model=configuracoesTodasTodas[0].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td><label>{{configuracoesTodasTodas[0].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-integrador/pages/home/view/dialogs/informe.dialog.html","<md-dialog aria-label=Informes flex=60 ng-cloak><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>Informes</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><md-content><div class=md-dialog-content ng-show=controleExibicao><div class=informe-item ng-repeat=\"item in informes\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><p class=informe-data>{{item.dataPublicacao | date : \'dd/MM/yyyy\'}}</p><h2 class=informe-titulo>{{item.titulo}}</h2><span class=informe-descricao ng-bind-html=descricaoExtensa(item.corpo)></span><div layout=row layout-align=\"end end\"><md-button class=\"btn-continue-lendo continue-informe\" ng-click=exibirDescricao(item)>continue lendo</md-button></div></div></div><md-table-pagination style=\"margin: auto; background-color: #5bf869\" ng-show=controleExibicao ng-show=\"informes.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findTodosInformes md-page-select></md-table-pagination></md-content><div class=md-dialog-content ng-show=!controleExibicao><div class=informe-item><h2 class=informe-titulo-v2>{{informeSelecionado.titulo}}</h2><p class=informe-data-v2>Publicado em {{informeSelecionado.dataPublicacao | date : \'dd/MM/yyyy\'}}</p><div ng-if=informeSelecionado.imagem class=informe-caixa-img><img ng-src={{gerarSrcImagem(informeSelecionado.imagem)}} height=150 width=300></div><span ng-bind-html=informeSelecionado.corpo></span><md-button class=\"md-primary md-raised\" ng-click=voltar()>Voltar</md-button></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-integrador/pages/home/view/dialogs/mensagem.dialog.html","<md-dialog aria-label=Mensagens flex=60 ng-cloak><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>{{mensagemSelecionada.assunto}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><p ng-bind-html=mensagemSelecionada.mensagem><md-button class=\"md-primary md-raised\" ng-click=voltarParaDlgMensagens($event)>Voltar</md-button></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-integrador/pages/home/view/dialogs/mensagens.dialog.html","<md-dialog aria-label=Mensagens flex=60 ng-cloak><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>Mensagens</h2><span flex></span><md-button class=\"md-icon-button md-icon-fechar\" ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><p class=mensagem ng-repeat=\"item in mensagens\" ng-model-options=\"{trackBy: \'$value.codigo\'}\" ng-click=\"mensagemLida($index); exibirMensagem($event, $index)\"><a ng-class=\"(item.lida == false ? \'msg-nao-lida\' : \'msg-lida\')\" href=#>{{item.dataEnvio | date : \'dd/MM/yyyy\'}} - {{item.assunto}}</a></p></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-integrador/pages/home/view/dialogs/pendencias.dialog.html","<md-dialog aria-label=Pendências ng-cloak><form><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>{{tituloPendencia}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row ng-show=\"pendencias.length>0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>Situação</th><th md-column>Quantidade</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"pendencia in pendencias\"><td md-cell class=celula><a ng-click=abrirPendencia(pendencia) ng-href={{pendencia.caminhoFuncionalidade}} target=_blank>{{pendencia.pendencia}}</a></td><td md-cell class=celula>{{pendencia.quantidade}}</td></tr></tbody></table></md-table-container></md-content></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/informes/view/dialog/preVisualizar.html","<md-dialog aria-label=Informe ng-cloak><form><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>Visualizar Informe</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=column><md-content flex><h2>{{informe.titulo}}</h2><h5>Data de Publicação: {{informe.dataPublicacao ? informe.dataPublicacao : dataPublicacaoInforme | date : \'dd/MM/yyyy\'}}</h5></md-content><md-content flex ng-show=imagem><div layout=row layout-align=\"center center\"><img alt=\"\" title={{imagem.name}} src={{imagem.src}} style=\"width: 336px; height: 123px\"></div></md-content><md-content flex><div ng-bind-html=renderCorpoHtml()></div></md-content></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/partials/checklistEspecífico.html","<md-dialog aria-label=\"{{\'label-checklist-configurar-componetes\' | translate | uppercase}}\" flex=60 ng-cloak><md-toolbar><div class=md-toolbar-tools><h2 ng-if=!checkListCtrl.modoConsulta>{{\'label-item-checklist-especifico\' | translate}}</h2><h2 ng-if=checkListCtrl.modoConsulta>{{\'label-consulta-checklist-especifico\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=checkListCtrl.fechar()><md-icon md-svg-src=assets/img/icons/ic_close_white_24px.svg aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=column><label>{{\'label-item-checklist-especifico\' | translate}}</label><md-input-container flex=100 class=textangular><div text-angular ng-model=checkListCtrl.checkList.descricao ta-toolbar=checkListCtrl.camposEdicaoTextAngular ta-max-text=400 md-select-on-focus></div></md-input-container><md-input-container class=md-block flex=100><label>{{\'label-fundamento-legal\' | translate}}</label><md-select ng-model=checkListCtrl.checkList.fundamentoLegalList multiple ng-model-options=\"{trackBy: \'$value.descricao\'}\"><md-option ng-repeat=\"item in checkListCtrl.fundamentosLegaisCadastrados\" ng-value=item><span ng-bind-html=checkListCtrl.formatarString(item.descricao)></span></md-option></md-select></md-input-container><div layout=row layout-align=\"end end\" ng-if=\"!checkListCtrl.editar && !checkListCtrl.modoConsulta\"><md-button class=md-raised ng-click=checkListCtrl.adicionarCheckList()>{{\'label-inserir\' | translate}}</md-button><md-button class=md-raised ng-click=checkListCtrl.ativarModoConsulta()>{{\'label-modo-consulta\' | translate}}</md-button></div><div layout=row layout-align=\"end end\" ng-if=\"!checkListCtrl.editar && checkListCtrl.modoConsulta\"><md-button class=md-raised ng-click=checkListCtrl.consultarCheckList()>{{\'label-consultar\' | translate}}</md-button><md-button class=md-raised ng-click=checkListCtrl.ativarModoInclusao()>{{\'label-modo-inclusao\' | translate}}</md-button></div><div layout=row layout-align=\"end end\" ng-if=checkListCtrl.editar><md-button class=md-raised ng-click=checkListCtrl.confirmarEdicao()>{{\'label-confirmar-edicao\' | translate}}</md-button><md-button class=md-raised ng-click=checkListCtrl.cancelarEdicao()>{{\'label-cancelar-edicao\' | translate}}</md-button></div><div ng-if=\"!checkListCtrl.modoConsulta && checkListCtrl.instrumento.listChecklist.length > 0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th></th><th md-column class=texto-esquerdo>{{\'label-item\' | translate}}</th><th md-column class=texto-centro>{{\'label-fundamento-legal\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in checkListCtrl.instrumento.listChecklist\n                        | limitTo: checkListCtrl.tabelaCheckList.limit:\n                        (checkListCtrl.tabelaCheckList.page - 1) * checkListCtrl.tabelaCheckList.limit\"><td md-cell class=\"margin-top tamanho-setas\"><div flex=10><i class=\"material-icons hand\" ng-click=\"checkListCtrl.ordemCampos($index, $index-1, checkListCtrl.instrumento.listChecklist)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"checkListCtrl.ordemCampos($index, $index+1, checkListCtrl.instrumento.listChecklist)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div layout=row layout-align=start><label ng-bind-html=item.descricao></label></div></td><td md-cell class=input-esquerda><div layout=row layout-align=center ng-repeat=\"fundamento in item.fundamentoLegalList\"><span ng-bind-html=checkListCtrl.formatarString(fundamento.descricao)></span></div></td><td md-cell layout=row layout-align=center><div layout=row layout-align=center class=margin-top><i class=\"material-icons hand\" ng-click=\"checkListCtrl.editarObjeto(checkListCtrl.instrumento.listChecklist, item )\">mode_edit</i><md-switch class=md-primary ng-if=checkListCtrl.formatoEdicao() ng-model=item.ativo></md-switch><i class=\"material-icons hand\" ng-if=!item.registroVinculado ng-click=\"checkListCtrl.removerItem($event, item, checkListCtrl.instrumento.listChecklist)\">delete</i></div></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"checkListCtrl.instrumento.listChecklist.length > 0\" md-limit=checkListCtrl.tabelaCheckList.limit md-limit-options=checkListCtrl.tabelaCheckList.limitsPage md-page=checkListCtrl.tabelaCheckList.page md-total={{checkListCtrl.instrumento.listChecklist.length}} md-on-paginate=true></md-table-pagination></md-content></div><div ng-if=\"checkListCtrl.modoConsulta && checkListCtrl.checkListConsulta.length > 0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th></th><th md-column class=texto-esquerdo>{{\'label-item\' | translate}}</th><th md-column class=texto-centro>{{\'label-fundamento-legal\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in checkListCtrl.checkListConsulta\n                        | limitTo: checkListCtrl.tabelaCheckList.limit:\n                        (checkListCtrl.tabelaCheckList.page - 1) * checkListCtrl.tabelaCheckList.limit\"><td md-cell class=\"margin-top tamanho-setas\"><div flex=10><i class=\"material-icons hand\" ng-click=\"checkListCtrl.ordemCampos($index, $index-1, checkListCtrl.instrumento.listChecklist)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"checkListCtrl.ordemCampos($index, $index+1, checkListCtrl.instrumento.listChecklist)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div layout=row layout-align=start><label ng-bind-html=item.descricao></label></div></td><td md-cell class=input-esquerda><div layout=row layout-align=center ng-repeat=\"fundamento in item.fundamentoLegalList\"><span ng-bind-html=checkListCtrl.formatarString(fundamento.descricao)></span></div></td><td md-cell layout=row layout-align=center><div layout=row layout-align=center class=margin-top><i class=\"material-icons hand\" ng-click=\"checkListCtrl.editarObjeto(checkListCtrl.checkListConsulta, item )\">mode_edit</i><md-switch class=md-primary ng-if=checkListCtrl.formatoEdicao() ng-model=item.ativo></md-switch><i class=\"material-icons hand\" ng-if=!item.registroVinculado ng-click=\"checkListCtrl.removerItem($event, item, checkListCtrl.instrumento.listChecklist)\">delete</i></div></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"checkListCtrl.instrumento.listChecklist.length > 0\" md-limit=checkListCtrl.tabelaCheckList.limit md-limit-options=checkListCtrl.tabelaCheckList.limitsPage md-page=checkListCtrl.tabelaCheckList.page md-total={{checkListCtrl.instrumento.listChecklist.length}} md-on-paginate=true></md-table-pagination></md-content></div><div class=margin-top layout=row layout-align=\"end center\" ng-if=!checkListCtrl.modoConsulta><md-button class=md-raised ng-click=checkListCtrl.confirmar()>{{\'label-confirmar\' | translate}}</md-button></div></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/partials/fundamentoLegal.html","<md-dialog aria-label=\"{{\'label-checklist-configurar-componetes\' | translate | uppercase}}\" flex=60 ng-cloak><md-toolbar><div class=md-toolbar-tools><h2 ng-if=!fundamentoCtrl.modoConsulta>{{\'label-fundamento-legal\' | translate}}</h2><h2 ng-if=fundamentoCtrl.modoConsulta>{{\'label-consulta-fundamento-legal\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=fundamentoCtrl.fechar()><md-icon md-svg-src=assets/img/icons/ic_close_white_24px.svg aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row class=align-tabela><md-content flex><div layout=column><label>{{\'label-fundamento-legal\' | translate}}</label><md-input-container flex=100 class=textangular><div text-angular required ta-toolbar=fundamentoCtrl.camposEdicaoTextAngular ng-model=fundamentoCtrl.fundamentoLegal.descricao ta-max-text=100 md-select-on-focus></div></md-input-container><div layout=row layout-align=\"end end\" ng-if=\"!fundamentoCtrl.editar && !fundamentoCtrl.modoConsulta\"><md-button class=md-raised ng-click=fundamentoCtrl.adicionarFundamentoLegal()>{{\'label-inserir\' | translate}}</md-button><md-button class=md-raised ng-click=fundamentoCtrl.ativarModoConsulta()>{{\'label-modo-consulta\' | translate}}</md-button></div><div layout=row layout-align=\"end end\" ng-if=\"!fundamentoCtrl.editar && fundamentoCtrl.modoConsulta\"><md-button class=md-raised ng-click=fundamentoCtrl.consultarFundamentoLegal()>{{\'label-consultar\' | translate}}</md-button><md-button class=md-raised ng-click=fundamentoCtrl.ativarModoInclusao()>{{\'label-modo-inclusao\' | translate}}</md-button></div><div layout=row layout-align=\"end end\" ng-if=fundamentoCtrl.editar><md-button class=md-raised ng-click=fundamentoCtrl.confirmarEdicao()>{{\'label-confirmar-edicao\' | translate}}</md-button><md-button class=md-raised ng-click=fundamentoCtrl.cancelarEdicao()>{{\'label-cancelar-edicao\' | translate}}</md-button></div><div ng-if=\"!fundamentoCtrl.modoConsulta && fundamentoCtrl.instrumento.listFundamentoLegais.length > 0 \"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th></th><th md-column class=texto-esquerdo>{{\'label-fundamento-legal\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"fundamento in fundamentoCtrl.instrumento.listFundamentoLegais\n                        | limitTo: fundamentoCtrl.tabelaFundamentos.limit:\n                        (fundamentoCtrl.tabelaFundamentos.page - 1) * fundamentoCtrl.tabelaFundamentos.limit\"><td md-cell class=\"margin-top tamanho-setas\"><div flex=10><i class=\"material-icons hand\" ng-click=\"fundamentoCtrl.ordemCampos($index, $index-1, fundamentoCtrl.instrumento.listFundamentoLegais)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"fundamentoCtrl.ordemCampos($index, $index+1, fundamentoCtrl.instrumento.listFundamentoLegais)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div layout=row layout-align=start><label ng-bind-html=fundamento.descricao></label></div></td><td md-cell layout=row layout-align=center><div layout=row layout-align=center class=margin-top><i class=\"material-icons hand\" ng-click=\"fundamentoCtrl.editarObjeto(fundamentoCtrl.instrumento.listFundamentoLegais, fundamento )\">mode_edit</i><md-switch class=md-primary ng-model=fundamento.ativo ng-if=fundamentoCtrl.formatoEdicao()></md-switch><i class=\"material-icons hand\" ng-if=!fundamento.registroVinculado ng-click=\"fundamentoCtrl.removerItem($event, fundamento, fundamentoCtrl.instrumento.listFundamentoLegais)\">delete</i></div></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"fundamentoCtrl.instrumento.listFundamentoLegais.length > 0\" md-limit=fundamentoCtrl.tabelaFundamentos.limit md-limit-options=fundamentoCtrl.tabelaFundamentos.limitsPage md-page=fundamentoCtrl.tabelaFundamentos.page md-total={{fundamentoCtrl.instrumento.listFundamentoLegais.length}} md-on-paginate=true></md-table-pagination></md-content></div><div ng-if=\"fundamentoCtrl.modoConsulta && fundamentoCtrl.fundamamentoLegalConsulta.length > 0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th></th><th md-column class=texto-esquerdo>{{\'label-fundamento-legal\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"fundamento in fundamentoCtrl.fundamamentoLegalConsulta\n                        | limitTo: fundamentoCtrl.tabelaFundamentos.limit:\n                        (fundamentoCtrl.tabelaFundamentos.page - 1) * fundamentoCtrl.tabelaFundamentos.limit\"><td md-cell class=\"margin-top tamanho-setas\"></td><td md-cell class=input-esquerda><div layout=row layout-align=start><label ng-bind-html=fundamento.descricao></label></div></td><td md-cell layout=row layout-align=center><div layout=row layout-align=center class=margin-top><i class=\"material-icons hand\" ng-click=\"fundamentoCtrl.editarObjeto(fundamentoCtrl.fundamamentoLegalConsulta, fundamento )\">mode_edit</i><md-switch class=md-primary ng-model=fundamento.ativo ng-if=fundamentoCtrl.formatoEdicao()></md-switch><i class=\"material-icons hand\" ng-if=!fundamento.registroVinculado ng-click=\"fundamentoCtrl.removerItem($event, fundamento, fundamentoCtrl.fundamamentoLegalConsulta)\">delete</i></div></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"fundamentoCtrl.fundamamentoLegalConsulta.length > 0\" md-limit=fundamentoCtrl.tabelaFundamentos.limit md-limit-options=fundamentoCtrl.tabelaFundamentos.limitsPage md-page=fundamentoCtrl.tabelaFundamentos.page md-total={{fundamentoCtrl.fundamamentoLegalConsulta.length}} md-on-paginate=true></md-table-pagination></md-content></div><div class=margin-top layout=row layout-align=\"end center\" ng-if=!fundamentoCtrl.modoConsulta><md-button class=md-raised ng-click=fundamentoCtrl.confirmar()>{{\'label-confirmar\' | translate}}</md-button></div></div></md-content></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/partials/justificativa.html","<md-dialog aria-label=Suspender flex=60 ng-cloak><form name=justificativaCtrl.formSuspender><md-toolbar><div class=\"md-toolbar-tools text-center\"><h2>{{\'label-suspensao\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=justificativaCtrl.cancelar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row flex=100 layout-margin><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}*</label><textarea ng-model=justificativaCtrl.funcionalidade.justificativa required name=justificativa maxlength=200 md-maxlength=200 ng-required=true></textarea></md-input-container></div><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=justificativaCtrl.cancelar() class=md-raised>Cancelar</md-button><md-button ng-click=justificativaCtrl.suspenderInstrumento() class=md-raised>Confirmar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/partials/removerFundamento.html","<md-dialog aria-label=\"Remover Fundamento Legal\" flex=40 ng-cloak><form name=removerFundamento id=teste><md-dialog-content><div class=md-dialog-content><h2 class=md-title>{{\'label-remover-fundamento-legal\' | translate}}</h2><p>{{\'label-confirmacao-remover-fundamento\' | translate}}</p></div><div layout=row flex=100 layout-align=\"end center\" class=md-dialog-actions><md-button ng-click=removerFundamentoCtrl.fechar() class=md-raised><span>Cancelamento</span></md-button><md-button ng-click=removerFundamentoCtrl.confirmar() class=md-raised><span>Confirmação</span></md-button></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/partials/suspenderInstrumento.html","<div style=\"margin: 20px\"><form name=formSuspender><titulo prefixo-titulo=\"\'Suspender\'\"></titulo><div layout=row><div layout=column flex=50><label>{{\'label-responsavel\' | translate}}:</label><b><p>{{responsavel.nome}} / {{responsavel.cpf | cpfcnpj}}</p></b></div><div layout=column flex=50><label>{{\'label-data\' | translate}}:</label><b><p>{{suspender.dataSuspensao | date: \'dd/MM/yyyy\'}}</p></b></div></div><div layout=row flex=100><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=suspender.justificativa required name=\"{{\'label-justificativa\' | translate}}\" maxlength=500 md-maxlength=500></textarea></md-input-container></div><div style=\"margin-top: 20%\"><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=cancelar() class=md-raised>{{\'btn-cancelar\' | translate}}</md-button><md-button ng-click=confirmar() class=md-raised>{{\'btn-confirmar\' | translate}}</md-button></div></div></form></div>");
$templateCache.put("scripts-integrador/pages/orientacao-tecnica/view/partials/modalLink.html","<md-dialog aria-label=\"Adicionar Link de Referência\" flex=40 ng-cloak><form ng-submit=adicionarLink()><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>Adicionar Link de Referência</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content layout=column layout-margin=5><md-input-container><label>Link</label><input ng-model=link.descricaoLink required ng-disabled=consultar></md-input-container><md-input-container><label>Tipo de Ato/Normativo</label><md-select ng-model=link.tipoOrientacaoTecnica required ng-disabled=consultar><md-option ng-repeat=\"tipo in tiposOrientacaoTecnica\" ng-selected=\"tipo.id == link.tipoOrientacaoTecnica.id\" ng-value=tipo>{{tipo.descricao}}</md-option></md-select><div class=md-errors-spacer></div></md-input-container><md-input-container><label>Descrição do Ato/Normativo</label><input ng-model=link.descricaoOrientacaoTecnica maxlength=300 md-maxlength=300 required ng-disabled=consultar></md-input-container><div layout=row><md-input-container flex=50><label>Número do Ato/Normativo</label><input type=text ng-model=link.numeroOrientacaoTecnica required ng-disabled=consultar></md-input-container><md-input-container flex=50><label>Data do Ato/Normativo</label><md-datepicker ng-model=link.dateOrientacaoTecnica required ng-disabled=consultar></md-datepicker></md-input-container></div><div layout=row layout-align=end><input class=\"md-raised md-button md-ink-ripple botao-formulario-modal\" type=submit value=inserir ng-if=!consultar></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/app/directives/secoes/templates/partials/modalAdicionarEditorHtml.html","<md-dialog aria-label={{titulo}} flex=60 ng-cloak><form name=form><md-toolbar><div class=md-toolbar-tools><h2>{{titulo}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row><label>{{descricao}}</label></div><div layout=row><text-angular ng-model=item.descricao name=corpo ta-toolbar-button-class=\"btn btn-default\" ta-toolbar-button-active-class=active></text-angular></div><div layout=row layout-sm=column layout-align=\"end center\" class=top-2><md-button class=md-raised ng-click=fechar()>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised ng-click=confirmar()>{{\'btn-confirmar\' | translate}}</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/app/directives/secoes/templates/partials/modalClausulas.html","<md-dialog aria-label=\"{{\'label-checklist-analise-tecnica\' | translate}}\" flex=60 ng-cloak id=su-integrador><form name=formClausula ng-submit=confirmar() novalidate><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-clausulas\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro></th><th md-column class=texto-centro>{{\'label-clausulas\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"clausula in listClausulas\"><td md-cell class=\"celula tamanho-setas\"><div flex=10><i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index-1, listClausulas)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index+1, listClausulas)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=\"celula tabela-clausula\" class=tamanho-setas><md-input-container layout=row flex-offset=10 flex><label>{{getNumeroPorExtenso($index)}}</label><input type=text required ng-model=clausula.descricao ng-maxlength=100 maxlength=100></md-input-container></td><td md-cell class=\"celula deletar\" layout-aling=\"center center\"><a href=\"\" ng-click=remover($index); style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22><md-tooltip>{{\'excluir\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></md-content></div><div layout=row layout-align=end><md-button class=md-raised style=\"margin-left: 0\" ng-click=adicionarClausula()>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row layout-sm=column layout-align=\"end center\" class=top-2><md-button class=md-raised ng-click=fechar()>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised type=submit>{{\'btn-confirmar\' | translate}}</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/app/directives/utilitarios/titulo/template/titulo.html","<h2 class=tituloFormulario>{{titulo}}</h2>");}]);
(function () {
  'use strict';

  angular.module('su-integrador', [
    'su-componentes'
  ]);

})();

  (function(){
    'use strict';
    angular.module('su-integrador')
      .controller('suspenderInstrumentoController',
        ['$scope', 'escopoCompartilhadoService','$state', '$filter', '$mdDialog','ComumFactory','instrumentoFuncionalidadeService',
          function ($scope,escopoCompartilhadoService,$state, $filter, $mdDialog, ComumFactory, instrumentoFuncionalidadeService) {

            var vm = $scope;

            vm.suspender = {};
            vm.responsavel = {};
            vm.suspender.dataSuspensao = new Date();


            vm.cancelar = cancelar;
            vm.confirmar = confirmar;


            function init() {
              vm.suspender.instrumentoFuncionalidadeCadastrado = escopoCompartilhadoService.getObjeto('funcionalidade');
              escopoCompartilhadoService.limparEscopo();
              if(!vm.suspender.instrumentoFuncionalidadeCadastrado) {
                window.history.back();
              }
              instrumentoFuncionalidadeService.getUsuario().then(function (resultado) {
                vm.responsavel.nome = resultado.data.resposta[0].nome;
                vm.responsavel.cpf = resultado.data.resposta[0].cpf;
                vm.suspender.responsavel = vm.responsavel.nome + ' / ' + vm.responsavel.cpf;
              })

            }

            function confirmar(ev) {
              if(vm.formSuspender.$invalid){
                var mensagem = $filter('translate')('msg-justificativa');
                ComumFactory.mostrarMensagemError(mensagem);
                return ;
              }
              var confirm = $mdDialog.confirm()
                .title('Suspender Instrumento')
                .textContent('Tem certeza que deseja suspender este instrumento/funcionalidade?')
                .targetEvent(ev)
                .ok('Confirmar')
                .cancel('Cancelar');
              $mdDialog.show(confirm).then(function() {
                suspenderInstrumento();

              }, function() {
              });
            }

            function cancelar(){
              escopoCompartilhadoService.limparEscopo();
              $state.go('INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE');
            }

            function suspenderInstrumento() {
              var mensagem;
              if(vm.suspender.instrumentoFuncionalidadeCadastrado.suspenso === true){
                mensagem = $filter('translate')('msg-instrumento-funcionalidade-suspendida');
              }else{
                mensagem = $filter('translate')('msg-instrumento-funcionalidade-n-suspensa');
              }
              instrumentoFuncionalidadeService.suspenderInstrumento(vm.suspender).then(function () {
                ComumFactory.mostrarMensagemSucesso(mensagem);
                $state.go('INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE');
              });

            }

            init();

          }]);
  })();

/**
 * Created by haillanderson on 15/02/17.
 */

(function () {
  'use strict';
  controller.$inject = ["$mdDialog"];
  angular
    .module('su-integrador')
    .controller('removerFundamentoLegalController', controller);

  function controller($mdDialog) {

    var vm = this;

    vm.fechar = fechar;
    vm.cancelar = cancelar;
    vm.confirmar = confirmar;
    vm.result;

    function fechar() {
      cancelar();
    }

    function cancelar() {
      vm.result = false;
      $mdDialog.hide(vm.result);
    }

    function confirmar() {
      vm.result= true;
      $mdDialog.hide(vm.result);
    }


  }
})();

(function () {
  'use strict';
  controller.$inject = ["instrumentoFuncionalidadeService", "$mdDialog", "funcionalidade", "$filter", "comumService"];
  angular
    .module('su-integrador')
    .controller('justificativaController', controller);

  function controller(instrumentoFuncionalidadeService, $mdDialog, funcionalidade, $filter, comumService ) {

    var vm = this;

    vm.fechar = fechar;
    vm.cancelar = cancelar;
    vm.confirmar = confirmar;
    vm.suspenderInstrumento = suspenderInstrumento;

    vm.funcionalidade = funcionalidade;

    function suspenderInstrumento(funcionalidade, ev) {
      if(vm.formSuspender.$invalid){
        var mensagem = $filter('translate')('msg-justificativa');
        comumService.mostrarMensagemError(mensagem);
        return ;
      }
      var confirm = $mdDialog.confirm()
        .title('Suspender Instrumento')
        .textContent('Tem certeza que deseja suspender este instrumento/funcionalidade?')
        .targetEvent(ev)
        .ok('Confirmação')
        .cancel('Cancelamento');
      $mdDialog.show(confirm).then(function() {
        confirmar(vm.funcionalidade);
      }, function() {
        cancelar();
      });
    }

    function fechar() {
      $mdDialog.cancel();
    }

    function cancelar() {
      vm.funcionalidade.funcionalidadeSuspenca = false;
      fechar()
    }

    function confirmar() {
      instrumentoFuncionalidadeService.salvar(vm.funcionalidade);
      fechar();
    }


  }
})();

/**
 * Created by Basis Tecnologia on 21/12/2016.
 */
(function () {
  'use strict';
  controller.$inject = ["instrumentoFuncionalidadeService", "ComumFactory", "escopoCompartilhadoService", "$mdDialog", "instrumento", "listaFundamentosAlterados", "idEdicao", "$filter"];
  angular
    .module('su-integrador')
    .controller('fundamentoLegalController', controller);

  function controller(instrumentoFuncionalidadeService, ComumFactory, escopoCompartilhadoService,
                      $mdDialog, instrumento, listaFundamentosAlterados, idEdicao, $filter) {

    var vm = this;

    var setarOrdem =  function(lista) {
      angular.forEach(lista, function (item, $index) {
        item.ordem = $index;
      });
      return lista;
    };

    var init = function () {
      vm.ordemCampos = instrumentoFuncionalidadeService.ordenaLista;
      inicializarFundamento();
    };
    vm.listaFundamentosAlterados = listaFundamentosAlterados;
    vm.instrumento = instrumento;
    vm.idEdicao = idEdicao;
    vm.indexTemporario = '';
    vm.editar = false;


    vm.camposEdicaoTextAngular =
      [
        ['h1', 'h2', 'h3', 'p'],
        ['bold', 'italics', 'underline', 'ul', 'ol'],
        ['justifyLeft', 'justifyCenter', 'justifyRight', 'indent', 'outdent'],
        ['html','insertLink']
      ];

    vm.tabelaFundamentos = {
      order:'ordem',
      limit: 5,
      limitsPage: [5, 10, 20],
      page: 1
    };

    vm.adicionarFundamentoLegal = adicionarFundamentoLegal;
    vm.confirmarEdicao = confirmarEdicao;
    vm.cancelarEdicao = cancelarEdicao;
    vm.editarObjeto = editarObjeto;
    vm.removerItem = removerItem;
    vm.confirmar = confirmar;
    vm.salvarFundamentoLegalTemp = salvarFundamentoLegalTemp;
    vm.fechar = fechar;
    vm.ativarModoConsulta = ativarModoConsulta;
    vm.ativarModoInclusao = ativarModoInclusao;
    vm.consultarFundamentoLegal = consultarFundamentoLegal;
    vm.inicializarFundamento = inicializarFundamento;
    vm.formatoEdicao = formatoEdicao;

    function formatoEdicao() {
      return vm.idEdicao;
    }

    function inicializarFundamento() {
      vm.fundamentoLegal = {id:'', descricao:'', ativo: true, registroVinculado: false, ordem: 0};
    }

    function adicionarFundamentoLegal(){
      if(vm.fundamentoLegal.descricao == ''){
        ComumFactory.mostrarMensagemError('Preencher todos os campos');
        return;
      }
      if(vm.instrumento.listFundamentoLegais == null || vm.instrumento.listFundamentoLegais == undefined){
          vm.instrumento.listFundamentoLegais = [];
      }
      vm.instrumento.listFundamentoLegais.push(vm.fundamentoLegal);
      inicializarFundamento();
    }

    function cancelarEdicao() {
      vm.editar = false;
      inicializarFundamento();
    }

    function editarObjeto(lista, fundamento) {
      vm.editar = true;
      vm.fundamentoLegal = angular.copy(lista[lista.indexOf(fundamento)]);
      vm.fundamentoLegal.descricao = ComumFactory.removerHTML(vm.fundamentoLegal.descricao);
      vm.indexTemporario = lista.indexOf(fundamento);
    }

    function confirmarEdicao() {
      if(vm.modoConsulta){
        vm.fundamamentoLegalConsulta[vm.indexTemporario] = vm.fundamentoLegal;
        var id =  vm.fundamamentoLegalConsulta[vm.indexTemporario].id;
        var descricao =  vm.fundamamentoLegalConsulta[vm.indexTemporario].descricao;
        enviarParaListaInstrumento(id, descricao);
      }else{
        vm.instrumento.listFundamentoLegais[vm.indexTemporario] = vm.fundamentoLegal;
      }
      vm.listaFundamentosAlterados.itensAlterados.push(vm.fundamentoLegal);
      vm.editar = false;
      inicializarFundamento();
    }

    function enviarParaListaInstrumento(id, descricao){
      angular.forEach( vm.instrumento.listFundamentoLegais, function(item){
            if(item.id == id){
              item.descricao = descricao;
            }
        });
    }

    function removerItem(ev, fundamento, lista) {
      if(verificarFundamentosVinculadosEmChecklist(fundamento)){
        $mdDialog.show({
          preserveScope: true,
          autoWrap: true,
          skipHide: true,
          controller: 'removerFundamentoLegalController',
          controllerAs: 'removerFundamentoCtrl',
          templateUrl: 'scripts-integrador/pages/instrumento-funcionalidade/view/partials/removerFundamento.html',
          parent: angular.element(document.body),
          targetEvent: ev,
          clickOutsideToClose: false
        }).then(function(result){
            if(result){
              removerFundamentoChecklist(fundamento, lista);
            }
        })
      }else{
        removerFundamentoChecklist(fundamento, lista);
      }
    }

    function removerFundamentoChecklist(fundamento, lista){
      vm.listaFundamentosAlterados.itensExcluidos.push(fundamento);
      lista.splice(lista.indexOf(fundamento), 1);
      vm.instrumento.listFundamentoLegais = lista;
      vm.fundamamentoLegalConsulta = lista;
    }

    function verificarFundamentosVinculadosEmChecklist(fundamento) {
      vm.verificador = false;
      angular.forEach(vm.instrumento.listChecklist, function (listaCheckLists) {
        angular.forEach(listaCheckLists.fundamentoLegalList, function (fundamentoLegal) {
          if(fundamento.id == fundamentoLegal.id){
             vm.verificador = true;
          }
        });
      });
      return vm.verificador;
    }

    function confirmar() {
      setarOrdem(vm.instrumento.listFundamentoLegais);
      vm.salvarFundamentoLegalTemp();
    }

    function salvarFundamentoLegalTemp(){
      vm.result = {
        instrumento: vm.instrumento,
        listaFundamentosAlterados: vm.listaFundamentosAlterados
      };

      instrumentoFuncionalidadeService.salvarFundamentoLegalTemp(vm.instrumento).then(function(retorno){
        vm.instrumento.listFundamentoLegais = retorno.data.resposta;
        $mdDialog.hide(vm.result);
        escopoCompartilhadoService.setObjetos('listFundamentoLegal', vm.instrumento.listFundamentoLegais);
      });
    }

    function fechar() {
      $mdDialog.cancel();
    }

    function ativarModoConsulta() {
      vm.modoConsulta = true;
      vm.editar = false;
      vm.fundamamentoLegalConsulta = [];
      inicializarFundamento();
    }

    function ativarModoInclusao() {
      vm.modoConsulta = false;
      vm.editar = false;
      inicializarFundamento();
    }
    function consultarFundamentoLegal() {
      instrumentoFuncionalidadeService.buscarFundamentoLegalPorDescricao(vm.instrumento.id, vm.fundamentoLegal.descricao).then(function(retorno){
        vm.fundamamentoLegalConsulta = retorno.data.resposta;
        if(vm.fundamamentoLegalConsulta.length == 0){
          ComumFactory.mostrarMensagemError($filter('translate')('msg-nenhum-registro-encontrado'));
          return;
        }
      })
    }

    init();
  }
})();

/**
 * Created by Basis Tecnologia on 21/12/2016.
 */
(function () {
  'use strict';
  controller.$inject = ["instrumentoFuncionalidadeService", "ComumFactory", "escopoCompartilhadoService", "$mdDialog", "instrumento", "idEdicao", "$filter"];
  angular
    .module('su-integrador')
    .controller('checkListController', controller);

  function controller(instrumentoFuncionalidadeService, ComumFactory, escopoCompartilhadoService,
                      $mdDialog, instrumento, idEdicao, $filter) {

    var vm = this;

    var setarOrdem =  function(lista) {
      angular.forEach(lista, function (item, $index) {
        item.ordem = $index;
      });
      return lista;
    };

    var init = function () {
      vm.fundamentosLegaisCadastrados = escopoCompartilhadoService.getObjeto('listFundamentoLegal');
      vm.ordemCampos = instrumentoFuncionalidadeService.ordenaLista;
      console.log(vm.fundamentosLegaisCadastrados);
      inicializarCheckList();
    };
    vm.idEdicao = idEdicao;
    vm.instrumento = instrumento;
    vm.indexTemporario = '';
    vm.editar = false;

    vm.camposEdicaoTextAngular =
      [
        ['h1', 'h2', 'h3', 'p'],
        ['bold', 'italics', 'underline', 'ul', 'ol'],
        ['justifyLeft', 'justifyCenter', 'justifyRight', 'indent', 'outdent'],
        ['html','insertLink']
      ];

    vm.tabelaCheckList = {
      order:'ordem',
      limit: 5,
      limitsPage: [5, 10, 20],
      page: 1
    };

    vm.adicionarCheckList = adicionarCheckList;
    vm.confirmarEdicao = confirmarEdicao;
    vm.cancelarEdicao = cancelarEdicao;
    vm.editarObjeto = editarObjeto;
    vm.removerItem = removerItem;
    vm.confirmar = confirmar;
    vm.fechar = fechar;
    vm.formatarString = formatarString;
    vm.ativarModoConsulta = ativarModoConsulta;
    vm.ativarModoInclusao = ativarModoInclusao;
    vm.inicializarCheckList = inicializarCheckList;
    vm.formatoEdicao = formatoEdicao;
    vm.consultarCheckList = consultarCheckList;

    function formatoEdicao() {
      return vm.idEdicao;
    }

    function inicializarCheckList() {
      vm.checkList = {
        id:'',
        descricao: "",
        fundamentoLegalList: [],
        fundamentoLegalListId:[],
        ativo: true,
        registroVinculado: false,
        ordem: 0
      };

    }

    function formatarString(item) {
      return ComumFactory.removerHTML(item);
    }

    function adicionarCheckList() {
      if(vm.checkList.descricao == ''){
        ComumFactory.mostrarMensagemError('Preencher todos os campos');
        return;
      }
      if(vm.instrumento.listChecklist == null || vm.instrumento.listChecklist == undefined){
          vm.instrumento.listChecklist = [];
      }
      vm.instrumento.listChecklist.push(vm.checkList);
      inicializarCheckList();
    }
    function confirmarEdicao() {
      if(vm.modoConsulta){
        vm.checkListConsulta[vm.indexTemporario] = vm.checkList;
        var id =  vm.checkListConsulta[vm.indexTemporario].id;
        var descricao =  vm.checkListConsulta[vm.indexTemporario].descricao;
        enviarParaListaInstrumento(id, descricao);
      }else{
        vm.instrumento.listChecklist[vm.indexTemporario] = vm.checkList;
      }
      vm.editar = false;
      inicializarCheckList();
    }

    function enviarParaListaInstrumento(id, descricao) {
      angular.forEach( vm.instrumento.listChecklist, function(item){
        if(item.id == id){
          item.descricao = descricao;
        }
      });
    }

    function cancelarEdicao() {
      vm.editar = false;
    }

    function editarObjeto(lista, checklist) {
      vm.editar = true;
      vm.checkList = angular.copy(lista[lista.indexOf(checklist)]);
      vm.checkList.descricao = ComumFactory.removerHTML(vm.checkList.descricao);
      vm.indexTemporario = lista.indexOf(checklist);
    }

    function removerItem(ev, checklist, lista) {
      lista.splice(lista.indexOf(checklist), 1);
      vm.checkListConsulta = lista;
      vm.instrumento.listChecklist = lista;
    }

    function confirmar() {
      setarOrdem(vm.instrumento.listChecklist);
      $mdDialog.hide(vm.instrumento);
    }

    function fechar() {
      $mdDialog.cancel();
    }

    function ativarModoConsulta() {
      vm.modoConsulta = true;
      vm.editar = false;
      vm.checkListConsulta = [];
      inicializarCheckList();
    }


    function ativarModoInclusao() {
      vm.modoConsulta = false;
      vm.editar = false;
      inicializarCheckList();
    }

    function consultarCheckList() {
      //Tratamento para quando a lista de fundamentos for nula
      if(vm.checkList.fundamentoLegalList.length == 0 ){
        angular.forEach(vm.instrumento.listFundamentoLegais, function (item){
          vm.checkList.fundamentoLegalListId.push(item.id);
        });
      } else {
        angular.forEach(vm.checkList.fundamentoLegalList, function (item) {
          vm.checkList.fundamentoLegalListId.push(item.id);
        });
      }
      instrumentoFuncionalidadeService.buscarCheckListPorDescricao(vm.instrumento.id, vm.checkList).then(function(retorno){
        vm.checkListConsulta = retorno.data.resposta;
        if(vm.checkListConsulta.length == 0){
          ComumFactory.mostrarMensagemError($filter('translate')('msg-nenhum-registro-encontrado'));
          return;
        }
      })
      vm.checkList.fundamentoLegalListId = [];
    }


    init();
  }
})();

(function(){
  'use strict';

  angular.module('su-integrador')
    .controller('cancelarFundamentoLegalController',
      ["$scope", "$state", "fundamentoLegalService", "integradorEscopoCompartilhadoService", "$filter", "instrumentoFuncionalidadeService", "$mdDialog", "ComumFactory", function ($scope, $state, fundamentoLegalService, integradorEscopoCompartilhadoService,$filter,instrumentoFuncionalidadeService, $mdDialog, ComumFactory) {

        var vm = $scope;

        vm.fechar = fechar;
        vm.confirmar = confirmar;
        var init = function() {
          vm.cancelamento = {};
          vm.responsavel = {};
          vm.cancelamento.dataSuspensao = new Date();

          vm.fundamentoLegal = integradorEscopoCompartilhadoService.getObjeto('fundamentoLegal');
          if(!vm.fundamentoLegal) {
            window.history.back();
          }
          fundamentoLegalService.findById(vm.fundamentoLegal.idFundamento).then(function (resultado) {
              vm.cancelamento.fundamentoLegal = angular.copy(resultado.data.resposta[0]);
              integradorEscopoCompartilhadoService.limparEscopo();
              if(!vm.cancelamento.fundamentoLegal) {
                window.history.back();
              }

          });


          instrumentoFuncionalidadeService.getUsuario().then(function (resultado) {
            vm.responsavel.nome = resultado.data.resposta[0].nome;
            vm.responsavel.cpf = resultado.data.resposta[0].cpf;
            vm.cancelamento.responsavel = vm.responsavel.nome + ' / ' + vm.responsavel.cpf;
          })
        };


        function confirmar(ev) {
          if(vm.formCancelar.$invalid){
            var mensagem = $filter('translate')('Necessário preencher os campos obrigatórios');
            ComumFactory.mostrarMensagemError(mensagem);
            return ;
          }
          var confirm = $mdDialog.confirm()
            .title('Suspender Instrumento')
            .textContent('Tem certeza que deseja cancelar esse fundamento legal?')
            .targetEvent(ev)
            .ok('Confirmar')
            .cancel('Cancelar');
          $mdDialog.show(confirm).then(function() {
              cancelarFundamento()
          }, function() {

          });
        }

        function cancelarFundamento() {
          var mensagem;

          mensagem = $filter('translate')('msg-dado-cancelado-com-sucesso');

          fundamentoLegalService.cancelar(vm.cancelamento).then(function () {
            ComumFactory.mostrarMensagemSucesso(mensagem);
            $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
          });

        }

        function fechar(){
          $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
        }

        init();
      }]);
})();

(function () {

  directive.$inject = ["$rootScope"];
  angular
    .module('su-integrador')
    .directive('titulo', directive);

  function directive($rootScope) {
    return {
      restrict: 'E',
      templateUrl: 'scripts-integrador/app/directives/utilitarios/titulo/template/titulo.html',
      replace: true,
      scope: {
          prefixoTitulo: '='
      },
      link: function (scope) {
        $rootScope.$watch('paginaAtual', function (newValue) {
            if (newValue) {
                if (scope.prefixoTitulo) {
                    scope.titulo = scope.prefixoTitulo + ' ' + newValue;
                } else {
                    scope.titulo = newValue;
                }
            }
        });

      }
    };
  }


})();

/**
 * Created by Basis Tecnologia on 28/07/2016.
 */
angular.module('su-integrador')
    .directive("verificarPermissaoPendencia", ["$rootScope", function ($rootScope) {
            return ({
                link: function link($scope, element, attributes) {
                    var roles = (attributes.verificarPermissaoPendencia || "").split(",");

                    $rootScope.$watch('usuarioLogado', function(value){
                        if(value && $rootScope.usuarioLogado.permissoes.length > 0){
                            for(var i = 0; i < roles.length; i++) {
                                if($rootScope.usuarioLogado.permissoes && $rootScope.usuarioLogado.permissoes.indexOf(roles[i].trim()) != -1) {
                                    break;
                                }else{
                                    //remover evento de click
                                    element.bind('click', function (e) {
                                        e.stopImmediatePropagation();
                                        return false;
                                    });
                                    break;
                                }
                            }
                        }
                    }, true);
                },
                restrict: "A"
            });
        }]
    );

(function(){
    'use strict';

        angular
        .module('su-integrador')
        .service('textosAjudaService', ['$http', service]);

        function service($http){

            var URL_BASE = 'integrador/api';

            var buscarModulos = function(){
                return $http.get(URL_BASE + '/modulos');
            };

            var buscarFuncionalidadePorModuloSemAjuda = function(id){
                return $http.get(URL_BASE + '/funcionalidade/pesquisar-por-modulo-sem-ajuda/' + id);
            };

          var buscarFuncionalidadePorModulo = function(id){
            return $http.get(URL_BASE + '/funcionalidade/pesquisar-por-modulo/' + id);
          };

            var salvarCamposAjuda = function (data) {
                return $http.post(URL_BASE + '/campo-ajuda/salvar',data);
            };

            var pesquisarFuncionalidadeComAjuda = function(query) {
              return $http.get(URL_BASE +'/funcionalidade/pesquisar', {
                params: {
                  idModulo: query.modulo.id,
                  idFuncionalidade: query.funcionalidade.id,
                  numeroPagina: query.numeroPagina,
                  maximoPorPagina: query.maximoPorPagina
                }
              });
            };

            var buscarCamposPorFuncionalidade = function(id){
              return $http.get(URL_BASE + '/campo-ajuda/pesquisar-por-funcionalidade-id/' + id);
            };

            return {
                buscarModulos: buscarModulos,
                buscarFuncionalidadePorModulo: buscarFuncionalidadePorModulo,
                pesquisarFuncionalidadeComAjuda: pesquisarFuncionalidadeComAjuda,
                buscarFuncionalidadePorModuloSemAjuda: buscarFuncionalidadePorModuloSemAjuda,
                buscarCamposPorFuncionalidade: buscarCamposPorFuncionalidade,
                salvarCamposAjuda: salvarCamposAjuda
            };
        }
})();

(function(){
    'use strict';
        angular
        .module('su-integrador')
        .controller('textosAjudaController',
                    ['$scope', '$mdDialog', 'atosContratosService', '$q', 'FormularioFactory', '$filter',
                      'ComumFactory', 'escopoCompartilhadoService', '$state', '$stateParams', 'localStorageService',
                      'instrumentoFuncionalidadeService', 'comumIntegradorService', 'textosAjudaService',
            function ($scope, $mdDialog, atosContratosService, $q, FormularioFactory, $filter, ComumFactory,
                      escopoCompartilhadoService, $state, $stateParams, localStorageService,
                      instrumentoFuncionalidadeService, comumIntegradorService, textosAjudaService) {


          var vm = this;
          vm.isEditar = false;
          var queryDefault = {
            maximoPorPagina: 20,
            numeroPagina:1
          };

          vm.query = angular.copy(queryDefault);

          var init = function(){
            vm.funcionalidades = [];
            textosAjudaService.buscarModulos()
              .then(function(resultado) {
                vm.modulos = resultado.data.resposta;
              });

            if ($stateParams.id) {
              vm.isEditar = true;
              var idFuncionalidade = $stateParams.id;
              textosAjudaService.buscarCamposPorFuncionalidade(idFuncionalidade).then(function(resultado) {
                vm.camposAjuda = resultado.data.resposta;
                vm.query.modulo = vm.camposAjuda[0].funcionalidade.modulo;
                textosAjudaService.buscarFuncionalidadePorModulo(vm.query.modulo.id)
                  .then(function(resultado){
                    vm.funcionalidades = resultado.data.resposta;
                    vm.query.funcionalidade = vm.camposAjuda[0].funcionalidade;
                  });
              });
            } else {
              vm.isEditar = false;
            }
          };

          vm.buscarFuncionalidadePorModulo = function(){
                textosAjudaService.buscarFuncionalidadePorModulo(vm.query.modulo.id)
                            .then(function(resultado){
                        vm.funcionalidades = resultado.data.resposta;
                });
          };

          vm.buscarFuncionalidadePorModuloSemAjuda = function(){
            textosAjudaService.buscarFuncionalidadePorModuloSemAjuda(vm.query.modulo.id)
              .then(function(resultado){
                vm.funcionalidades = resultado.data.resposta;
              });
          };

          vm.limpar = function () {
            vm.query = angular.copy(queryDefault);
          };

          vm.salvarTextosAjuda = function (cadastroTextosAjudaForm) {
            if(cadastroTextosAjudaForm.$invalid){
              cadastroTextosAjudaForm.$setSubmitted();
              ComumFactory.mostrarMensagemError('Necessário preencher os campos obrigatórios');
            }else{
              var campos = {campos: vm.camposAjuda};
              textosAjudaService.salvarCamposAjuda(campos).then(function () {
                cadastroTextosAjudaForm.$setPristine();
                vm.redirecionarTela('INTEGRADOR.TEXTOS_AJUDA');
                ComumFactory.mostrarMensagemSucesso('Dados salvos com sucesso');
              });
            }
          };

          vm.redirecionarTela = function (state) {
            vm.camposAjuda = {};
            $state.go(state);
          };

          vm.irParaEdicao = function (id) {
            $state.go('INTEGRADOR.EDITAR_TEXTOS_AJUDA', {id: id});
          };

          vm.pesquisar = function(){
            if (!vm.query.modulo) {
              vm.query.modulo = {};
            }
            if (!vm.query.funcionalidade) {
              vm.query.funcionalidade = {};
            }
            textosAjudaService.pesquisarFuncionalidadeComAjuda(vm.query)
              .then(function(resultado){
                vm.listaFuncionalidades = resultado.data.content;
                vm.query.totalElements = resultado.data.totalElements;
              });
          };

          vm.pesquisarCamposAjuda =function () {
            textosAjudaService.buscarCamposPorFuncionalidade(vm.query.funcionalidade.id).then(function(resultado) {
              vm.camposAjuda = resultado.data.resposta;
            });
          };

          init();

    }]);
})();

(function() {
	'use strict';
	angular.module('su-integrador')
		.controller('SiapaController',['$scope','$state', '$sce', function ($scope, $state, $sce) {

      if(!$state.params.urlMenu && !$state.params.url){
				$state.go('INTEGRADOR.HOME')	;
			}

			$scope.url = undefined;

			$scope.apresentarFrame = function () {
				if(angular.isUndefined($scope.url)) {
					$scope.url = $sce.trustAsResourceUrl($state.params.url);
				}
				if(angular.isUndefined($scope.url)) {
					$scope.url = $sce.trustAsResourceUrl($state.params.urlMenu);
				}
				return angular.isDefined($scope.url);
			};


		}])
})();

(function () {

  'use strict';

  service.$inject = ["$http"];
  angular
    .module('su-integrador')
    .factory('procedimentoOperacionalPadraoService', service);

  function service ($http) {

    var url = "integrador/api/pop";

    var salvar = function(pop){
      return $http.post(url + "/salvar/", pop);
    };

    var findByTitulo = function(titulo){
      return $http.get(url + "/findByTitulo/" + titulo);
    };

    var findById = function(id){
      return $http.get(url + "/" + id);
    };

    return {
      salvar: salvar,
      findByTitulo: findByTitulo,
      findById: findById
    }

  }

})();

(function () {
  'use strict';
  angular
    .module('su-integrador')
    .controller('procedimentoOperacionalPadraoController',
      ['$scope', 'ComumFactory', 'procedimentoOperacionalPadraoService', 'arquivoServiceIntegrador', '$state', 'integradorEscopoCompartilhadoService',
        function ($scope, ComumFactory, procedimentoOperacionalPadraoService, arquivoServiceIntegrador, $state, integradorEscopoCompartilhadoService) {

          var vm = $scope;

          vm.pop = {
            listArquivoFluxo: [],
            listArquivoAuxiliar: [],
            isRascunho: false
          };

          var init = function(){
            vm.orientacaoTecnica = integradorEscopoCompartilhadoService.getObjeto('orientacaoTecnica');
            vm.indexPop = integradorEscopoCompartilhadoService.getObjeto('indexPop');
            vm.rotaRetorno = integradorEscopoCompartilhadoService.getObjeto('rotaRetorno');
            if(!vm.orientacaoTecnica){
              vm.voltar();
            } else {
              if(vm.indexPop !== undefined){
                if(integradorEscopoCompartilhadoService.getObjeto('consultarPop')){
                  vm.consultar = true;
                } else {
                  vm.editar = true;
                }
              }
              vm.pesquisarPop();
            }
          };

          vm.pesquisarPop = function(){
            if(vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop] && vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop].edited){
              vm.pop = vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop];
            } else {
              if(angular.isUndefined(vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop])){
                return;
              }
              procedimentoOperacionalPadraoService.findById(vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop].id).then(function(resp){
                vm.pop = resp.data.resposta[0];
              });
            }
          };

          vm.setRascunho = function(){
            vm.pop.isRascunho = true;
          };

          vm.uploadArquivoFluxo = function(arquivo) {
            arquivoServiceIntegrador.uploadArquivo(arquivo[0]).then(function(resp){
              vm.pop.listArquivoFluxo.push({id: resp.data.resposta[0], nome: arquivo[0].name});
            });
          };

          vm.uploadArquivoAuxiliar = function(arquivo) {
            arquivoServiceIntegrador.uploadArquivo(arquivo[0]).then(function(resp){
              vm.pop.listArquivoAuxiliar.push({id: resp.data.resposta[0], nome: arquivo[0].name});
            });
          };

          vm.salvar = function(){
            if(!vm.formularioPop.$valid || !vm.pop.procedimento || (vm.pop.isRascunho && !vm.pop.titulo)){
              ComumFactory.mostrarMensagemError("Necessário preencher os campos obrigatórios");
              vm.pop.isRascunho = false;
              return;
            }
            procedimentoOperacionalPadraoService.findByTitulo(vm.pop.titulo).then(function(resp){
              if(resp.data.resposta.length > 0 && !vm.editar){
                ComumFactory.mostrarMensagemError("O Nome/Titulo informado já se encontra cadastrado.");
                vm.pop.isRascunho = false;
              } else {
                if(vm.editar){
                  vm.pop.edited = true;
                  vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[vm.indexPop] = vm.pop;
                } else {
                  vm.orientacaoTecnica.listProcedimentoOperacionalPadrao.push(vm.pop);
                }
                integradorEscopoCompartilhadoService.setObjetos('orientacaoTecnica', vm.orientacaoTecnica);
                vm.voltar();
              }
            });
          };

          vm.voltar = function(){
            if(!vm.rotaRetorno){
              $state.go('INTEGRADOR.CONSULTA_ORIENTACAO_TECNICA');
            } else {
              $state.go(vm.rotaRetorno.name, {id: vm.orientacaoTecnica.id});
            }
          };

          init();

        }]);
})();

(function () {

  'use strict';

  service.$inject = ["$http"];
  angular
    .module('su-integrador')
    .factory('orientacaoTecnicaService', service);

  function service ($http) {

    var url = "integrador/api/orientacaoTecnica";
    var urlComum =  "integrador/api/comum";

    var salvar = function(orientacaoTecnica){
      return $http.post(url + "/salvar/", orientacaoTecnica);
    };

    var consultar = function(id){
      return $http.get(url + "/" + id);
    };

    var getTiposOrientacaoTecnica = function(){
      return $http.get(urlComum + "/tipos-orientacao-tecnica/");
    };

    var getAreasOrientacaoTecnica = function(){
      return $http.get(urlComum + "/areas-orientacao-tecnica/");
    };

    var getAssuntosOrientacaoTecnica = function(){
      return $http.get(urlComum + "/assuntos-orientacao-tecnica/");
    };

    return {
      salvar: salvar,
      getTiposOrientacaoTecnica: getTiposOrientacaoTecnica,
      getAreasOrientacaoTecnica: getAreasOrientacaoTecnica,
      getAssuntosOrientacaoTecnica: getAssuntosOrientacaoTecnica,
      consultar: consultar
    }

  }

})();

(function () {
  'use strict';
  angular
    .module('su-integrador')
    .controller('orientacaoTecnicaController',
      ['$scope', 'ComumFactory', '$mdDialog', 'orientacaoTecnicaService', '$state', '$stateParams', 'integradorEscopoCompartilhadoService',
        function ($scope, ComumFactory, $mdDialog, orientacaoTecnicaService, $state, $stateParams, integradorEscopoCompartilhadoService) {

          var vm = $scope;

          vm.orientacaoTecnica = {
            listLinkOrientacaoTecnica: [],
            listProcedimentoOperacionalPadrao: [],
            isRascunho: false
          };
          vm.areasOrientacaoTecnica = [];
          vm.assuntosOrientacaoTecnica = [];
          vm.tiposOrientacaoTecnica = [];

          var init = function(){
            var orientacaoTecnica = integradorEscopoCompartilhadoService.getObjeto('orientacaoTecnica');
            integradorEscopoCompartilhadoService.limparEscopo();
            if(orientacaoTecnica){
              vm.orientacaoTecnica = orientacaoTecnica;
            } else if($stateParams.id){
              orientacaoTecnicaService.consultar($stateParams.id).then(function(resp){
                vm.orientacaoTecnica = resp.data.resposta[0];
              });
            }
            if($state.$current.name === 'INTEGRADOR.ORIENTACAO_TECNICA') {
              $scope.consultar = true;
            }

            orientacaoTecnicaService.getAreasOrientacaoTecnica().then(function(resp){
              vm.areasOrientacaoTecnica = resp.data.resposta;
            });

            orientacaoTecnicaService.getAssuntosOrientacaoTecnica().then(function(resp){
              vm.assuntosOrientacaoTecnica = resp.data.resposta;
            });

            orientacaoTecnicaService.getTiposOrientacaoTecnica().then(function(resp){
              vm.tiposOrientacaoTecnica = resp.data.resposta;
            });

          };

          vm.abrirModalLink = function(index, consultar){
            var link = undefined;
            if(index !== undefined){
              link = vm.orientacaoTecnica.listLinkOrientacaoTecnica[index];
            }

            $mdDialog.show({
              controller: ["$scope", "tiposOrientacaoTecnica", "link", "consultar", function ($scope, tiposOrientacaoTecnica, link, consultar) {
                var init = function(){
                  $scope.consultar = consultar;
                  if(link){
                    $scope.link = angular.copy(link);
                    $scope.link.dateOrientacaoTecnica = new Date($scope.link.dateOrientacaoTecnica);
                    $scope.editar = true;
                  }
                };
                $scope.tiposOrientacaoTecnica = tiposOrientacaoTecnica;
                $scope.adicionarLink = function(){
                  if(!$scope.editar){
                    vm.orientacaoTecnica.listLinkOrientacaoTecnica.push($scope.link);
                  } else {
                    vm.orientacaoTecnica.listLinkOrientacaoTecnica[index] = $scope.link;
                  }
                  $scope.closeDialog();
                };
                $scope.closeDialog = function () {
                  $mdDialog.cancel();
                };
                init();
              }],
              templateUrl: 'scripts-integrador/pages/orientacao-tecnica/view/partials/modalLink.html',
              parent: angular.element(document.body),
              clickOutsideToClose: true,
              locals: {
                tiposOrientacaoTecnica: vm.tiposOrientacaoTecnica,
                link: link,
                consultar: consultar
              }
            });
          };

          vm.deletarLink = function(index){
            var link = vm.orientacaoTecnica.listLinkOrientacaoTecnica[index];
            if(link.id && link.id != null){
              link.isExcluido = true;
            } else {
              vm.orientacaoTecnica.listLinkOrientacaoTecnica.splice(index, 1);
            }
          };

          vm.editarPop = function(index){
            integradorEscopoCompartilhadoService.setObjetos('orientacaoTecnica', vm.orientacaoTecnica);
            integradorEscopoCompartilhadoService.setObjetos('indexPop', index);
            integradorEscopoCompartilhadoService.setObjetos('rotaRetorno', $state.$current);
            $state.go('INTEGRADOR.CADASTRO_PROCEDIMENTO_OPERACIONAL_PADRAO');
          };

          vm.visualizarPop = function(index){
            integradorEscopoCompartilhadoService.setObjetos('orientacaoTecnica', vm.orientacaoTecnica);
            integradorEscopoCompartilhadoService.setObjetos('indexPop', index);
            integradorEscopoCompartilhadoService.setObjetos('consultarPop', true);
            console.log($state.$current)
            integradorEscopoCompartilhadoService.setObjetos('rotaRetorno', $state.$current);
            $state.go('INTEGRADOR.CADASTRO_PROCEDIMENTO_OPERACIONAL_PADRAO');
          };

          vm.deletarPop = function(index){
            var pop = vm.orientacaoTecnica.listProcedimentoOperacionalPadrao[index];
            if(pop.id && pop.id != null){
              pop.isExcluido = true;
            } else {
              vm.orientacaoTecnica.listProcedimentoOperacionalPadrao.splice(index, 1);
            }
          };

          vm.inserirPop = function(){
            integradorEscopoCompartilhadoService.setObjetos('orientacaoTecnica', vm.orientacaoTecnica);
            integradorEscopoCompartilhadoService.setObjetos('rotaRetorno', $state.$current);
            $state.go('INTEGRADOR.CADASTRO_PROCEDIMENTO_OPERACIONAL_PADRAO');
          };

          vm.setRascunho = function(){
            vm.orientacaoTecnica.isRascunho = true;
          };

          vm.salvar = function(){
            if(vm.orientacaoTecnicaForm.$valid){
              orientacaoTecnicaService.salvar(vm.orientacaoTecnica).then(function(resp){
                $state.go('INTEGRADOR.CONSULTA_ORIENTACAO_TECNICA');
              });
            }
          };

          vm.voltar = function(){
            $state.go('INTEGRADOR.CONSULTA_ORIENTACAO_TECNICA');
          };

          init();

        }]);
})();

(function () {

  'use strict';

  service.$inject = ["$http"];
  angular
    .module('su-integrador')
    .factory('instrumentoFuncionalidadeService', service);

  function service ($http) {

    var buscarModulos = function(){
      return $http.get('integrador/api/modulos');
    };

    var buscarTodos = function() {
      return $http.get('integrador/api/instrumento-funcionalidade/pesquisar-todos');
    };

    var buscarFuncionalidadePorModulo = function(id){
      return $http.get('integrador/api/instrumento-funcionalidade/pesquisar-por-modulo/' + id);
    };


    var filterFindAll = function(query) {

      query.modulo =  query.modulo || {};
      query.funcionalidade =  query.funcionalidade || {};
      return $http.get('integrador/api/instumento-funcionalidade-cadastrado/pesquisar-instrumento-funcionalidade/', {
        params: {
          idModulo: query.modulo.id,
          idFuncionalidade: query.funcionalidade.id,
          numeroPagina: query.numeroPagina,
          maximoPorPagina: query.maximoPorPagina
        }
      });
    };

    var desfazer = function () {
      return $http.post('integrador/api/instrumento-funcionalidade/desfazer')
    };

    var salvar = function (funcionalidade) {
      return $http.post('integrador/api/instrumento-funcionalidade/salvar', funcionalidade);
    };

    var salvarFundamentoLegalTemp = function (funcionalidade) {
        return $http.post('integrador/api/fundamento-legal/salvar-fundamento-legal-temp', funcionalidade);
    };

    var buscarFundamentosLegais = function (idFuncionalidade) {
        return $http.get('integrador/api/fundamento-legal/pesquisar-fundamento-legal/'+ idFuncionalidade);
    };

    var buscarFundamentoLegalPorDescricao = function (idFuncionalidade, descricao) {
        return $http.get('integrador/api/fundamento-legal/pesquisar-fundamento-legal-por-descricao',{params:{
          'idFuncionalidade': idFuncionalidade,
          'descricao': descricao
      }});
    };

    var salvarInstrumento = function (funcionalidade) {
      return $http.post('integrador/api/instumento-funcionalidade-cadastrado/salvar', funcionalidade);
    };

    var buscarDadosInstrumento = function (id) {
      return $http.get('integrador/api/instrumento-funcionalidade/pesquisar-instrumento/'+ id);
    };

    var buscarCheckListPorDescricao = function (idFuncionalidade, checkList) {
      return $http.get('integrador/api/checklist/pesquisar-checklist-por-descricao',
        {params:{
          'idFuncionalidade':idFuncionalidade,
          'descricao': checkList.descricao,
          'fundamentoLegalListId': checkList.fundamentoLegalListId
        }});
    };

    var ordenaLista = function (atual, destino, lista) {
      var item = lista[atual];
      lista[atual] = lista[destino];
      lista[destino] = item;
    };

    var buscarInstrumentos = function (idModulo) {
      return $http.get('integrador/api/instrumento-funcionalidade/instrumentos-nao-cadastrados/' + idModulo);
    };

    var suspenderInstrumento = function (instrumento) {
      return $http.post('integrador/api/instumento-funcionalidade-cadastrado/suspender/', instrumento);
    };

    var getUsuario = function () {
      return $http.get('integrador/api/usuario/usuario-logado');
    };

    var buscarInstrumentosCadastrados = function(){
      return $http.get('integrador/api/instumento-funcionalidade-cadastrado/pesquisar-instrumento-funcionalidade');
    };

    var buscarInstrumentosCadastradosAll = function(){
      return $http.get('integrador/api/instumento-funcionalidade-cadastrado/pesquisar-instrumento-funcionalidade-cadastrados');
    };

    return {
      buscarModulos: buscarModulos,
      buscarFuncionalidadePorModulo: buscarFuncionalidadePorModulo,
      filterFindAll : filterFindAll,
      salvar: salvar,
      desfazer: desfazer,
      ordenaLista: ordenaLista,
      buscarFundamentosLegais: buscarFundamentosLegais,
      buscarFundamentoLegalPorDescricao: buscarFundamentoLegalPorDescricao,
      buscarDadosInstrumento: buscarDadosInstrumento,
      salvarFundamentoLegalTemp: salvarFundamentoLegalTemp,
      buscarCheckListPorDescricao: buscarCheckListPorDescricao,
      buscarTodos: buscarTodos,
      salvarInstrumento: salvarInstrumento,
      buscarInstrumentos: buscarInstrumentos,
      suspenderInstrumento: suspenderInstrumento,
      getUsuario: getUsuario,
      buscarInstrumentosCadastrados: buscarInstrumentosCadastrados,
      buscarInstrumentosCadastradosAll: buscarInstrumentosCadastradosAll
    };

  }

})();

/**
 * Created by basis on 19/12/16.
 */
(function(){
  'use strict';
  angular.module('su-integrador')
    .controller('consultaInstrumentoFuncionalidadeController',
      ['$scope','instrumentoFuncionalidadeService','$state','$mdDialog', 'escopoCompartilhadoService','integradorEscopoCompartilhadoService',
        function ($scope, instrumentoFuncionalidadeService, $state, $mdDialog, escopoCompartilhadoService, integradorEscopoCompartilhadoService) {
          var vm = $scope;

          vm.ativo = true;
          vm.paginacao = {
            limit: 5,
            limitsPage: [5, 10, 20],
            page: 1
          };

          vm.suspenderInstrumento = suspenderInstrumento;
          vm.buscarModulos = buscarModulos;
          vm.cadastrarNovoInstrumento = cadastrarNovoInstrumento;
          vm.buscarFuncionalidadePorModulo = buscarFuncionalidadePorModulo;
          vm.limparPesquisa = limparPesquisa;
          vm.filterAll = filterAll;
          vm.vincularFundamentoLegal = vincularFundamentoLegal;
          vm.editarFuncionalidade = editarFuncionalidade;

          function buscarModulos () {
            instrumentoFuncionalidadeService.buscarModulos()
              .then(function (resultado) {
                vm.modulos = resultado.data.resposta;
              });
          }

          function buscarFuncionalidadePorModulo(){
            if(vm.modulo.id){
              instrumentoFuncionalidadeService.buscarFuncionalidadePorModulo(vm.modulo.id)
                .then(function(resultado){
                  vm.funcionalidades = resultado.data.resposta;
                });
            }
          }

          function filterAll() {

            var parametros = {modulo: vm.modulo, funcionalidade: vm.instrumentoFuncionalidadeId, numeroPagina: vm.paginacao.page, maximoPorPagina: vm.paginacao.limit};

            instrumentoFuncionalidadeService.filterFindAll(parametros)
              .then(function(resultado){
                vm.funcionalidadesList = resultado.data.content;
                vm.totalItems = resultado.data.totalElements;
              });

          }

          function cadastrarNovoInstrumento (){
            $state.go('INTEGRADOR.CADASTRAR_INSTRUMENTO_FUNCIONALIDADE');
          }

          function limparPesquisa () {
            vm.modulo = {};
            vm.instrumentoFuncionalidadeId = {};
            vm.funcionalidades = [];
          }

          function suspenderInstrumento(funcionalidade) {
            escopoCompartilhadoService.setObjetos('funcionalidade', funcionalidade);
            $state.go('INTEGRADOR.SUSPENDER_INSTRUMENTO')
          }

          function vincularFundamentoLegal(funcionalidade) {
            vm.isVincular = true;
            vm.instrumentoId =  funcionalidade.instrumentoFuncionalidade.id;
            integradorEscopoCompartilhadoService.setObjetos('idInstrumentoFuncionalidade',vm.instrumentoId);
            integradorEscopoCompartilhadoService.setObjetos('isEditar',vm.isVincular);
            $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
          }

          function editarFuncionalidade(funcionalidade) {
            vm.editar = true;
            escopoCompartilhadoService.setObjetos('editar', vm.editar);
            escopoCompartilhadoService.setObjetos('funcionalidade', funcionalidade);
            $state.go('INTEGRADOR.EDITAR_INSTRUMENTO_FUNCIONALIDADE')
          }



          var init = function(){
            vm.buscarModulos();
            filterAll();
          };

          init();

        }]);
})();


(function () {
  'use strict';
  angular
    .module('su-integrador')
    .controller('cadastroInstrumentoFuncionalidadeController',
      ['$scope', '$mdDialog', 'escopoCompartilhadoService', 'instrumentoFuncionalidadeService', 'ComumFactory', '$stateParams', '$state','$filter','integradorEscopoCompartilhadoService',
        function ($scope, $mdDialog, escopoCompartilhadoService, instrumentoFuncionalidadeService, ComumFactory, $stateParams, $state, $filter,integradorEscopoCompartilhadoService) {

          var vm = $scope;
          var init = function () {
            vm.moduloId = null;
            vm.buscarModulos();


            if(escopoCompartilhadoService.getObjeto('editar') && escopoCompartilhadoService.getObjeto('funcionalidade')){
                vm.instrumento = escopoCompartilhadoService.getObjeto('funcionalidade');
                vm.isEdicao = escopoCompartilhadoService.getObjeto('editar');
                escopoCompartilhadoService.limparEscopo();
                vm.instrumento.modulo = vm.instrumento.instrumentoFuncionalidade.modulo;

                instrumentoFuncionalidadeService.buscarTodos().then(function(resultado) {
                  vm.funcionalidades = resultado.data.resposta;
                });
            }

          };

          vm.instrumento = {
            modulo: {}
          };


          vm.buscarModulos = buscarModulos;
          vm.buscarFuncionalidadePorModulo = buscarFuncionalidadePorModulo;
          vm.formatarString = formatarString;
          vm.verificarInstrumentoPreenchido = verificarInstrumentoPreenchido;
          vm.retornar = retornar;
          vm.salvarInstrumento = salvarInstrumento;
          vm.vincularFundamentoLegal = vincularFundamentoLegal;


          function verificarInstrumentoPreenchido() {
            return vm.instrumento.id;
          }

          function formatarString(item) {
            return ComumFactory.removerHTML(item);
          }

          function retornar() {
            instrumentoFuncionalidadeService.desfazer().then(function () {
              $state.go('INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE');
            })
          }


          function exibirMensagemErro(error) {
            var mensagens = [];
            angular.forEach(error.required, function (field) {
              if (field.$invalid) {
                mensagens.push(field.$name);
              }
            });

            ComumFactory.mostrarMensagemError($filter('translate')('msg-campos-obrigatorios'), mensagens);
          }

          function buscarModulos() {
            instrumentoFuncionalidadeService.buscarModulos()
              .then(function (resultado) {
                vm.modulos = resultado.data.resposta;
              });
          }

          function buscarFuncionalidadePorModulo() {
            if(!vm.isEdicao){
              if (vm.instrumento.modulo) {
                instrumentoFuncionalidadeService.buscarInstrumentos(vm.instrumento.modulo.id)
                  .then(function (resultado) {
                    vm.funcionalidades = resultado.data.resposta;

                  });
              }
            }
          }
          function salvarInstrumento() {
            if(vm.form.$invalid) {
              exibirMensagemErro(vm.form.$error);
              return;
            }

            instrumentoFuncionalidadeService.salvarInstrumento(vm.instrumento).then(function (resp) {
              vm.instrumento.id = resp.data.resposta[0].id;
              ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-documentos'));
              vm.isEdicao = true;

            });
          }

          function vincularFundamentoLegal() {
            vm.isVincular = true;
            vm.instrumentoId =  vm.instrumento.instrumentoFuncionalidade.id;
            integradorEscopoCompartilhadoService.setObjetos('idInstrumentoFuncionalidade',vm.instrumentoId);
            integradorEscopoCompartilhadoService.setObjetos('isEditar',vm.isVincular);
            $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
          }

          init();


        }]);
})();

/**
 * Created by Basis Tecnologia on 03/08/2016.
 */
(function(){
    'use strict';
    angular.module('su-integrador')
        .factory('informesService',['$http', function ($http) {

            var url = '/integrador/api/informes/';

            var findAll = function(inicial, limite){
                return $http.get(url + '?inicial=' + inicial + '&limite=' + limite);
            };

            var findInformesByFilter = function(filtros){
                var endPoint = url + "filtro?titulo="+filtros.titulo;
                if(filtros.dataPublicacaoInicio != null){
                    endPoint = endPoint + "&dataInicio="+filtros.dataPublicacaoInicio;
                }
                if(filtros.dataPublicacaoFim != null){
                    endPoint = endPoint + "&dataFim="+filtros.dataPublicacaoFim;
                }
                return $http.get(endPoint);
            };

            var deletar = function (id) {
                return $http.delete(url + '/' + id);
            };

            var persistir = function (informe) {
                return $http.post(url + 'salvar/', informe);
            };


          var listarInformesHome = function() {
            return $http.get(url + 'home');
          };

            return {
                findAll: findAll,
                findInformesByFilter: findInformesByFilter,
                deletar: deletar,
                persistir: persistir,
                listarInformesHome: listarInformesHome
            };
        }]);
})();

/**
 * Created by Basis Tecnologia on 03/08/2016.
 */
(function(){
    'use strict';
        angular.module('su-integrador')
        .controller('informesController',  ['$scope','$mdDialog','$state', 'informesService','escopoCompartilhadoService',
        function ($scope, $mdDialog,$state, informesService, escopoCompartilhadoService) {

            var vm = $scope;

            vm.listaInformes = [];
            vm.informe = {};

            vm.init = function(){
                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;
                vm.findAll();
                vm.clearFilter();
            };

            vm.findAll = function() {
                informesService.findAll((vm.registroInicial - 1), vm.tamanhoLimite).then(function(retorno){
                    vm.listaInformes = retorno.data.resposta[0].content;
                    vm.totalItems = retorno.data.resposta[0].totalElements;
                });
            };

            vm.findInformesByFilter = function() {
                informesService.findInformesByFilter(vm.filtros).then(function(retorno){
                    vm.listaInformes = retorno.data.resposta;
                    vm.totalItems = vm.listaInformes.length;
                });
            };


            vm.clearFilter = function() {
                vm.filtros = {
                    titulo: "",
                    dataPublicacaoInicio: null,
                    dataPublicacaoFim: null
                };
            };

            vm.showConfirmExcluir = function(ev, id, index) {
                var confirm = $mdDialog.confirm({focusOnOpen:false})
                    .title('Excluir Informe')
                    .textContent('Deseja realmente excluir o informe?')
                    .targetEvent(ev)
                    .ok('Sim')
                    .cancel('Não');
                $mdDialog.show(confirm).then(function() {
                    informesService.deletar(id).then(function(resposta){
                        if(resposta.status == '204')
                            vm.listaInformes.splice(index, 1);
                    });
                }, function() {
                    $mdDialog.cancel();
                });
            };

            vm.redirecionaTela = function(){
                escopoCompartilhadoService.setObjetos('informe',vm.informe);
                $state.go('INTEGRADOR.CADASTRAR_INFORME');
            };

            vm.carregaInformeEdicao = function(informe){
                vm.informe = informe;
                vm.redirecionaTela();
            };

            vm.init();

        }]);
})();

/**
 * Created by Basis Tecnologia on 03/08/2016.
 */
(function(){
    'use strict';
        angular.module('su-integrador')
        .controller('cadastroInformesController',  ['$scope','$mdDialog','$state',
                    'informesService','escopoCompartilhadoService',
                    'arquivoServiceIntegrador','textAngularManager', 'FormularioFactory',
        function ($scope, $mdDialog,$state, informesService, escopoCompartilhadoService,
                  arquivoServiceIntegrador, textAngularManager, FormularioFactory) {

          var vm = $scope;

          vm.SALVAR = 'SALVAR';
          vm.PRE_VISUALIZAR = 'PRE_VISUALIZAR';
          vm.acaoSubmit = '';
          vm.validarFormulario = FormularioFactory.validarFormularioComMensagem;

          vm.informe = {};

          var CONTENT_TYPE_IMAGE = ['image/png', 'image/tif', 'image/jpeg', 'image/jpg'];

          var init = function() {
              vm.informe = escopoCompartilhadoService.getObjeto('informe');
              vm.dataPublicacaoInforme = new Date();
              if (vm.informe) {
                vm.imagem = vm.informe.imagem;
                if (vm.informe.imagem) {
                  vm.imagem.name = vm.informe.imagem.nome;
                  vm.imagem.type = vm.informe.imagem.extencao;
                  vm.imagem.src = arquivoServiceIntegrador.gerarSRCImagemBase64(vm.informe.imagem);
                }
              }
          };

          vm.enviarFormulario = function(){
            if(vm.acaoSubmit == vm.SALVAR){
              gravarDados();
            }else if(vm.acaoSubmit == vm.PRE_VISUALIZAR){
              preVisualizar();
            }
          }

          vm.uploadImagem = function(imagem) {
            if (imagem[0].type && !validarExtensaoImagem(imagem[0].type)) {
                vm.imagem = imagem[0];
                gerarPreviewImagem(imagem[0]);
            }
          };

          var gerarPreviewImagem = function(imagem) {
              arquivoServiceIntegrador.gerarPreviewImagens(imagem, function (src) {
                vm.imagem.src = src;
              });
          };

          var validarExtensaoImagem = function (formato) {
            return CONTENT_TYPE_IMAGE.indexOf(formato) == -1;
          };

          var gravarDados = function() {
              vm.informe.dataPublicacaoInforme = new Date();
              vm.informe.dataPublicacao = null;

              if (vm.imagem) {
                  arquivoServiceIntegrador.uploadArquivo(vm.imagem).then(function (response) {
                    vm.informe.imagem = {
                      id: response.data.resposta[0]
                    };

                    salvarInformes(vm.informe);
                  });
              } else {
                vm.informe.imagem = null;
                salvarInformes(vm.informe);
              }
            };

            var salvarInformes = function(informe) {
                informesService.persistir(informe).then(function (retorno) {
                  $state.go('INTEGRADOR.INFORMES');
                });
            };

            vm.limpar = function() {

                vm.informe = {
                  dataPublicacaoInforme: new Date()
                };

                vm.informe.titulo = undefined;
                vm.informe.corpo = undefined;
                vm.informe.dataPublicacaoInforme = new Date();
                vm.informe.dataPublicacao = undefined;
                vm.informe.imagem = undefined;
                vm.imagem = undefined;
            };

            var preVisualizar = function(ev){
                $mdDialog.show({
                    controller: ["$scope", "$mdDialog", "informe", "imagem", "dataPublicacaoInforme", "$sce", function ($scope, $mdDialog, informe, imagem, dataPublicacaoInforme, $sce) {
                      $scope.informe = informe;
                      $scope.imagem = imagem;
                      $scope.dataPublicacaoInforme = dataPublicacaoInforme;

                      $scope.fechar = function () {
                        $mdDialog.cancel();
                      };

                      $scope.renderCorpoHtml = function(){
                        return $sce.trustAsHtml($scope.informe.corpo);
                      };

                    }],
                    templateUrl: 'scripts-integrador/pages/informes/view/dialog/preVisualizar.html',
                    parent: angular.element(document.body),
                    targetEvent: ev,
                    clickOutsideToClose: true,
                    locals: {
                        informe: vm.informe,
                        imagem: vm.imagem,
                        dataPublicacaoInforme: vm.dataPublicacaoInforme
                }
                });
            };

          init();
        }]);
})();

/**
 * Created by Basis Tecnologia on 13/07/2016.
 */
(function(){
    'use strict';

    PendenciaService.$inject = ["$http"];
    angular.module('su-integrador').factory('PendenciaService', PendenciaService);

    function PendenciaService($http){
        var url = '/integrador/api/dashboard/pendencia';

        var findPendenciaServicos = function(){
           return $http.get(url + "/servicos");
        };

        var findPendenciaCadastroImovel = function(){
            return $http.get(url + "/cadastro-imovel");
        };

        var findPendenciaAvaliacaoContabilidade = function(){
            return $http.get(url + "/avaliacao");
        };

        var findPendenciaDestinacao = function(){
            return $http.get(url + "/destinacao");
        };

        var findPendenciaReceitas = function(){
            return $http.get(url + "/receita");
        };

        var findPendenciaFiscalizacao = function(){
            return $http.get(url + "/fiscalizacao");
        };

        var findPendenciaGeoInformacoes = function(){
            return $http.get(url + "/geoinformacao");
        };

        return {
                findPendenciaServicos : findPendenciaServicos,
                findPendenciaCadastroImovel : findPendenciaCadastroImovel,
                findPendenciaAvaliacaoContabilidade: findPendenciaAvaliacaoContabilidade,
                findPendenciaDestinacao: findPendenciaDestinacao,
                findPendenciaReceitas:findPendenciaReceitas,
                findPendenciaFiscalizacao:findPendenciaFiscalizacao,
                findPendenciaGeoInformacoes:findPendenciaGeoInformacoes
        };
    }
})();

/**
 * Created by Basis Tecnologia on 12/08/2016.
 */
/**
 * Created by Basis Tecnologia on 13/07/2016.
 */
(function(){
    'use strict';

    PainelServicoService.$inject = ["$http"];
    angular.module('su-integrador').factory('PainelServicoService', PainelServicoService);

    function PainelServicoService($http){
        var url = '/integrador/api/posicaoCarteira';

        var getDadosPainel = function(){
            return $http.get(url);
        };

        var getPercentualPendentes = function(dadosPainelServico){
            return parseInt((dadosPainelServico.totalPendentes / dadosPainelServico.totalRequerimentos) * 100);
        };


        return {
            getDadosPainel: getDadosPainel,
            getPercentualPendentes: getPercentualPendentes
        };
    }
})();

/**
 * Created by Basis Tecnologia on 14/07/2016.
 */
(function () {
    'use strict';

    MensagemService.$inject = ["$http"];
    angular.module('su-integrador').factory('MensagemService', MensagemService);

    function MensagemService($http) {

        var url = '/integrador/api/dashboard/mensagem';

        var findMensagensServicos = function () {
            return $http.get(url + "/servicos");
        };

        var findMensagensCadastroImovel = function(){
            return [];
        };

        var findMensagensAvaliacaoContabilidade = function(){
            return [];
        };

        var findMensagensDestinacao = function(){
            return [];
        };

        var findMensagensReceitas = function(){
            return [];
        };

        var findMensagensFiscalizacao = function(){
            return [];
        };

        var findMensagensGeoInformacoes = function(){
            return [];
        };

        var alterarMensagemParaLida = function (idMensagem) {
            return $http.get(url + "/mensagemLida/" + idMensagem);
        };

        return{
            findMensagensServicos : findMensagensServicos,
            findMensagensCadastroImovel : findMensagensCadastroImovel,
            findMensagensAvaliacaoContabilidade : findMensagensAvaliacaoContabilidade,
            findMensagensDestinacao : findMensagensDestinacao,
            findMensagensReceitas : findMensagensReceitas,
            findMensagensFiscalizacao : findMensagensFiscalizacao,
            findMensagensGeoInformacoes : findMensagensGeoInformacoes,
            alterarMensagemParaLida : alterarMensagemParaLida
        }
    }
})();

/**
 * Created by Basis Tecnologia on 12/08/2016.
 */
/**
 * Created by Basis Tecnologia on 13/07/2016.
 */
(function(){
    'use strict';

    QuadroResumoService.$inject = ["$http"];
    angular.module('su-integrador').factory('QuadroResumoService', QuadroResumoService);

    function QuadroResumoService($http){
        var url = '/integrador/api/quadroResumo';

        var findAllQuadroResumo = function(){
            return $http.get(url);
        };

        var montarGrafico = function(titulo, dadosGrafico){
            var opcoesGrafico = {
                options: {
                    chart: {
                        type: 'pie',
                        plotBackgroundColor: null,
                        plotBorderWidth: null,
                        plotShadow: false,
                        width: 280

                    },
                    tooltip: {
                        pointFormat: '{point.key}: <b>{point.y}</b>',
                        headerFormat: ''
                    },
                    legend: {
                        labelFormat: '{key}'
                    },
                    plotOptions: {
                        pie: {
                            dataLabels: {
                                enabled: true,
                                format: '{point.percentage:.1f} %'
                            },
                            showInLegend: true
                        }

                    }

                },

                title: {
                    text: titulo
                },
                colorByPoint: true,
                series: [{
                    type: 'pie',
                    data: dadosGrafico
                }],


                loading: false
            };
            return opcoesGrafico;
        };

        return {
            findAllQuadroResumo: findAllQuadroResumo,
            montarGrafico: montarGrafico
        };
    }
})();

(function () {
  'use strict';
  angular.module('su-integrador')
    .controller('HomeIntegradorController', ['$scope', '$mdDialog', '$mdToast', 'PendenciaService', 'MensagemService', 'QuadroResumoService', 'PainelServicoService', 'ComumFactory', '$sce', 'informesService', 'arquivoServiceIntegrador', 'geovisIntegradorFactory','$timeout','$state',
      function ($scope, $mdDialog, $mdToast, PendenciaService, MensagemService, QuadroResumoService, PainelServicoService, ComumFactory, $sce, informesService, arquivoServiceIntegrador, geovisIntegradorFactory, $timeout, $state) {

        var self = this;
        self.teste = {};
        self.pendencias = [];
        self.tituloPendencia = "";
        $scope.urlServicos;
        $scope.urlDestinacao;
        // comumService.buscarUrlsMenusPorAmbiente().then(function (response) {
        //   var url = response.data;
        //   $scope.urlServicos = $sce.trustAsResourceUrl(url.servicos+'/#/servicos/triagem?status=AGUARDANDO_ANALISE_PREVIA');
        //   $scope.urlDestinacao = $sce.trustAsResourceUrl(url.urlDestinacao+'/#/doacao');
        // });

        /**
         * PENDÊNCIAS
         * */
        // PendenciaService.findPendenciaServicos().then(function(retorno){
        //   self.pendenciasServicos = retorno.data;
        // });
        PendenciaService.findPendenciaCadastroImovel().then(function(retorno){
          self.pendenciasCadastroImovel = retorno.data;
        });
        PendenciaService.findPendenciaAvaliacaoContabilidade().then(function(retorno){
          self.pendenciasAvaliacaoContabilidade = retorno.data;
        });
        // PendenciaService.findPendenciaDestinacao().then(function(retorno){
        //   self.pendenciasDestinacao = retorno.data;
        // });
        // PendenciaService.findPendenciaReceitas().then(function(retorno){
        //   self.pendenciasReceitas = retorno.data;
        // });
        // PendenciaService.findPendenciaFiscalizacao().then(function(retorno){
        //   self.pendenciasFiscalizacao = retorno.data;
        // });
        // PendenciaService.findPendenciaGeoInformacoes().then(function(retorno){
        //   self.pendenciasGeoInformacoes = retorno.data;
        // });


        /**
         * Dialog de pendências
         * */
        self.showPendenciasDialog = function (ev,tipoPendencia) {
          atribuiTipoPendencia(tipoPendencia);
          if(self.pendencias.length > 0 ){
            $mdDialog.show({
              controller: pendenciasDialogController,
              templateUrl: 'scripts-integrador/pages/home/view/dialogs/pendencias.dialog.html',
              parent: angular.element(document.body),
              targetEvent: ev,
              clickOutsideToClose: true,
              locals: {
                pendencias: self.pendencias,
                tituloPendencia: self.tituloPendencia
              }
            });
          }
        };

        var pendenciasDialogController = function ($scope, $mdDialog, pendencias, tituloPendencia) {
          $scope.pendencias = pendencias;
          $scope.tituloPendencia = tituloPendencia;

          $scope.abrirPendencia = function (pendencia) {
            window.sessionStorage.setItem('pendencia', JSON.stringify(pendencia));
          };

          $scope.fechar = function () {
            $mdDialog.cancel();
          };
        };
        pendenciasDialogController.$inject = ["$scope", "$mdDialog", "pendencias", "tituloPendencia"];

        var atribuiTipoPendencia = function(tipoPendencia){
          // desabilitando pesquisa de serviços pendentes
          // if(tipoPendencia === 1){
          //   self.pendencias = self.pendenciasServicos.pendencias;
          //   self.tituloPendencia = "Lista de Pendências - Serviços";
          // }
          if(tipoPendencia === 1){
            self.pendencias = [];
            self.tituloPendencia = "Lista de Pendências - Serviços";
          }
          if(tipoPendencia === 2){
            self.pendencias = self.pendenciasCadastroImovel.pendencias;
            self.tituloPendencia = "Lista de Pendências - Cadastro de imóveis";
          }
          if(tipoPendencia === 3){
            self.pendencias = self.pendenciasAvaliacaoContabilidade.pendencias;
            self.tituloPendencia = "Lista de Pendências - Avaliação/Contabilidade";
          }
          if(tipoPendencia === 4){
            self.pendencias = self.pendenciasDestinacao.pendencias;
            self.tituloPendencia = "Lista de Pendências - Destinação";
          }
          if(tipoPendencia === 5){
            self.pendencias = [];
            self.tituloPendencia = "Lista de Pendências - Receitas";
          }
          if(tipoPendencia === 6){
            self.pendencias = [];
            self.tituloPendencia = "Lista de Pendências - Fistalização";
          }
          if(tipoPendencia === 7){
            self.pendencias = [];
            self.tituloPendencia = "Lista de Pendências - Geo Informações";
          }
        };

        /**
         *	MENSAGENS
         */
        /** Comentado até definição da funcionalidade
         *
         MensagemService.findMensagensServicos().then(function(retorno){
				self.mensagens = retorno.data.mensagens;
				self.quantidadeMensagensNaoLidas = retorno.data.quantidadeMensagensNaoLidas;
			});
         self.mensagemSelecionada = {};

         self.mensagemLida = function (indexMensagem) {
				self.mensagemSelecionada = self.mensagens[indexMensagem];
				if (self.mensagemSelecionada.lida == false) {
					MensagemService.alterarMensagemParaLida(self.mensagemSelecionada.id).then(function(retorno){
						self.mensagemSelecionada.lida = retorno.data.lida;
						self.quantidadeMensagensNaoLidas -= 1;
					});
				}
			};


         self.showMensagemDialog = function (ev, indexMensagem) {
				$mdDialog.show({
					controller: mesagemDialogController,
					templateUrl: 'scripts-integrador/pages/home/view/dialogs/mensagem.dialog.html',
					parent: angular.element(document.body),
					targetEvent: ev,
					clickOutsideToClose: true,
					locals: {
						mensagemSelecionada: self.mensagens[indexMensagem]
					}
				});
			};

         var mesagemDialogController = function ($scope, $mdDialog, mensagemSelecionada) {
				$scope.mensagemSelecionada = mensagemSelecionada;

				$scope.fechar = function () {
					$mdDialog.cancel();
				};

				$scope.voltarParaDlgMensagens = function (ev) {
					$mdDialog.cancel();
					self.showMensagensDialog(ev);
				}
			};




         self.showMensagensDialog = function (ev) {
				$mdDialog.show({
					controller: mesagensDialogController,
					templateUrl: 'scripts-integrador/pages/home/view/dialogs/mensagens.dialog.html',
					parent: angular.element(document.body),
					targetEvent: ev,
					clickOutsideToClose: true,
					locals: {
						mensagens: self.mensagens
					}
				});
			};

         var mesagensDialogController = function ($scope, $mdDialog, mensagens) {
				$scope.mensagens = mensagens;

				$scope.fechar = function () {
					$mdDialog.cancel();
				};

				$scope.mensagemLida = function(indexMensagem){
					self.mensagemLida(indexMensagem);
				};

				$scope.exibirMensagem = function(ev, indexMensagem){
					$mdDialog.cancel();
					self.showMensagemDialog(ev, indexMensagem);
				};
			};

         */


        /*FUNCOES FAVORITAS*/

        self.redirecionaFuncaoFavorita = function () {
          alert("Redirecionando para função favorita....");
        };

        /*INFORMES*/

        var initInformes = function() {

          self.tamanhoLimite = 5;
          self.totalItems = 0;
          self.registroInicial = 1;

          self.informeSelecionado = {};
          self.findTodosInformes();

          informesService.listarInformesHome().then(function (response) {
            self.informesHome = response.data.resposta;
          });
        };

        self.findTodosInformes = function() {
          informesService.findAll((self.registroInicial - 1), self.tamanhoLimite).then(function(response) {
            self.informes = response.data.resposta[0].content;
            self.totalItems = response.data.resposta[0].totalElements;
          });
        };

        self.gerarSrcImagem = function(imagem) {
          return arquivoServiceIntegrador.gerarSRCImagemBase64(imagem);
        };

        self.resetInformeSelecionado = function () {
          self.informeSelecionado = {};
        };

        self.continuarLendo = function (informe) {
          self.informeSelecionado = informe;
        };

        self.showInformesDialog = function (ev) {
          $mdDialog.show({
            controller: informeDialogController,
            templateUrl: 'scripts-integrador/pages/home/view/dialogs/informe.dialog.html',
            parent: angular.element(document.body),
            targetEvent: ev,
            clickOutsideToClose: true,
            locals: {
              informes: self.informes,
              informeSelecionado: self.informeSelecionado,
              tamanhoLimite: self.tamanhoLimite,
              totalItems: self.totalItems,
              registroInicial: self.registroInicial,
              findTodosInformes: self.findTodosInformes
            }
          });
        };

        var informeDialogController = function ($scope, $mdDialog, informes, informeSelecionado,
                                                tamanhoLimite, totalItems, registroInicial, findTodosInformes, ComumFactory, arquivoServiceIntegrador, $sce) {
          $scope.informes = informes;
          $scope.informeSelecionado = informeSelecionado;

          $scope.tamanhoLimite = tamanhoLimite;
          $scope.totalItems = totalItems;
          $scope.registroInicial = registroInicial;

          $scope.findTodosInformes = findTodosInformes;

          $scope.fechar = function () {
            $mdDialog.cancel();
          };

          $scope.descricaoExtensa = function (descricaoInforme) {
            descricaoInforme = ComumFactory.removerHTML(descricaoInforme);

            if (descricaoInforme.length > 200) {
              descricaoInforme = descricaoInforme.substring(0, 200);
              descricaoInforme += "...";
              return descricaoInforme;
            } else {
              return descricaoInforme;
            }
          };

          $scope.gerarSrcImagem = function(imagem) {
            return arquivoServiceIntegrador.gerarSRCImagemBase64(imagem);
          };

          $scope.controleExibicao = true;
          $scope.exibirDescricao = function (informe) {
            $scope.informeSelecionado = informe;
            $scope.controleExibicao = false;
          };

          if (informeSelecionado.id !== undefined) {
            $scope.descricao = $sce.trustAsHtml(informeSelecionado.descricao);
            $scope.controleExibicao = false;
          }

          $scope.voltar = function () {
            $scope.informeSelecionado = {};
            $scope.controleExibicao = true;
          }

        };
        informeDialogController.$inject = ["$scope", "$mdDialog", "informes", "informeSelecionado", "tamanhoLimite", "totalItems", "registroInicial", "findTodosInformes", "ComumFactory", "arquivoServiceIntegrador", "$sce"];

        self.descricaoExtensa = function (descricaoInforme) {
          descricaoInforme = ComumFactory.removerHTML(descricaoInforme);

          if (descricaoInforme.length > 200) {
            descricaoInforme = descricaoInforme.substring(0, 120);
            descricaoInforme += "...";
            return descricaoInforme;
          } else {
            return descricaoInforme;
          }
        };

        initInformes();

        /**
         * Gráficos
         * */

        self.dadosGraficoQuantidade = [];
        self.dadosGraficoArea = [];
        self.dadosGraficoValorImovel = [];

        QuadroResumoService.findAllQuadroResumo().then(function(retorno){
          for(var i = 0; i < retorno.data.resposta.length; i++){
            var dadosQuantidade ={};
            dadosQuantidade.key = retorno.data.resposta[i].tipoInformacao;
            dadosQuantidade.y = retorno.data.resposta[i].quantidade;
            self.dadosGraficoQuantidade.push(dadosQuantidade);

            var dadosArea ={};
            dadosArea.key = retorno.data.resposta[i].tipoInformacao;
            dadosArea.y = retorno.data.resposta[i].valorAreaSoma;
            self.dadosGraficoArea.push(dadosArea);

            var dadosValorImovel ={};
            dadosValorImovel.key = retorno.data.resposta[i].tipoInformacao;
            dadosValorImovel.y = retorno.data.resposta[i].valorImovelSoma;
            self.dadosGraficoValorImovel.push(dadosValorImovel);


          }
          self.opcoesGraficoQuantidade = QuadroResumoService.montarGrafico('Quantidade',self.dadosGraficoQuantidade);
          self.opcoesGraficoArea = QuadroResumoService.montarGrafico('Área',self.dadosGraficoArea);
          self.opcoesGraficoValor = QuadroResumoService.montarGrafico('Valor',self.dadosGraficoValorImovel);

        });


        /**
         * Painel de Serviços
         *
         */
        self.dadosPainelServico = {};
        PainelServicoService.getDadosPainel().then(function(retorno){
          console.log(retorno.data.resposta);
          self.dadosPainelServico = retorno.data.resposta[0];
          self.dadosPainelServico.percentualPendentes = PainelServicoService.getPercentualPendentes(self.dadosPainelServico);
        });

        $timeout(function () {
            geovisIntegradorFactory.criaMapa();
        }, 1000);

        window.addEventListener("resize", function(event) {
          if (!!document.getElementById('homeMapGeovis')) {
            document.getElementById('homeMapGeovis').innerHTML = "";
            geovisIntegradorFactory.criaMapa();
          }
        })

        self.geovisPesquisar = function (){
            if(self.buscaGeovis != undefined){
                window.open("http://spunet-geovis.2.nuvem.gov.br/#/geovisualizador?busca="+self.buscaGeovis, "_blank");
            }
        }


        self.videos = [
          {
            image: 'assets/img/suporte.png'
          },
          {
            image: 'assets/img/brasil.png'
          }
        ];

      }])
})();

(function () {
    'use strict';
    angular
      .module('su-integrador')
      .factory('geovisIntegradorFactory', function () {
            
         var view;
         
         function criaMapa () {
                 
             var controls = ol.control.defaults({zoom: false});
             controls.extend([new ol.control.ScaleLine()]);
             var interactions = ol.interaction.defaults();
             
             var homeMapGeovis = new ol.Map({
              target: 'homeMapGeovis',
                  controls: controls,
                  interactions: interactions,
              view: new ol.View({
                projection: 'EPSG:4326',
                center: [-51.4198237555309, -14.2364826698601],
                zoom: 4.54
              })
             });

             var layerBase = new ol.layer.Tile({
                 source: new ol.source.OSM()
             });
             homeMapGeovis.addLayer(layerBase);
             view = homeMapGeovis.getView();
         }
        return {
            criaMapa: criaMapa
        };
});
})();



(function () {

  'use strict';

  service.$inject = ["$http"];
  angular
    .module('su-integrador')
    .factory('fundamentoLegalService', service);

  function service ($http) {

    var URL_FUNDAMENTO_LEGAL = 'integrador/api/fundamento-legal';

    var buscarCriteriosUtilizado = function(){
      return $http.get(URL_FUNDAMENTO_LEGAL + '/buscar-criterios-utilizado');
    };

    var buscarNaturezasPermitidas = function() {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/buscar-natureza-juridica');
    };

    var salvar = function(fundamentoLegal) {
      return $http.post(URL_FUNDAMENTO_LEGAL, fundamentoLegal);
    };

    var cancelar = function (fundamentoLegal) {
      return $http.post(URL_FUNDAMENTO_LEGAL + '/cancelar', fundamentoLegal);
    };

    var findDadosCancelamento = function (id) {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/fundamento-cancelado', {
        params:{
          id:id
        }
      })
    };

    var buscarFundamentosLegais = function(filtroConsulta, offset, limit) {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/consulta-paginacao', {
        params: {
          idInstrumentoFuncionalidade: filtroConsulta.idInstrumentoFuncionalidade,
          template: filtroConsulta.template,
          offset: offset,
          limit: limit
        }
      });
    };

    var pesquisarTodosFundamentos = function() {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/find-all');
    };

    var pesquisarTodosTiposIncorporacao = function() {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/tipo-incorporacao/all');
    };

    var pesquisarTodosTiposAquisicao = function() {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/tipo-aquisicao/all');
    };

    var pesquisarTodasEntidadesExtintas = function() {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/entidade-extinta/all');
    };

    var pesquisarTodosTipoUsoPermitido = function () {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/tipo-uso-permitido/all');
    };

    var pesquisarEspecificacao = function (utilizacao) {
      return $http.get(URL_FUNDAMENTO_LEGAL +'/buscar-especificacao?utilizacao=' + utilizacao);
    };

    var buscarTodasEspecificacoes  = function (utilizacao) {
      return $http.get(URL_FUNDAMENTO_LEGAL +'/especificacoes/all/');
    };

    var findById = function (id) {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/find-by-id',{
        params: {
          id: id
        }
      });
    };

    var buscarTiposAvaliacao = function () {
      return $http.get(URL_FUNDAMENTO_LEGAL + '/buscar-tipo-avaliacao');
    };

    return {
      buscarCriteriosUtilizado: buscarCriteriosUtilizado,
      buscarNaturezasPermitidas: buscarNaturezasPermitidas,
      salvar: salvar,
      buscarFundamentosLegais: buscarFundamentosLegais,
      pesquisarTodosFundamentos: pesquisarTodosFundamentos,
      pesquisarTodosTiposIncorporacao: pesquisarTodosTiposIncorporacao,
      pesquisarTodosTiposAquisicao: pesquisarTodosTiposAquisicao,
      pesquisarTodasEntidadesExtintas: pesquisarTodasEntidadesExtintas,
      pesquisarTodosTipoUsoPermitido: pesquisarTodosTipoUsoPermitido,
      pesquisarEspecificacao: pesquisarEspecificacao,
      buscarTodasEspecificacoes: buscarTodasEspecificacoes,
      cancelar: cancelar,
      findById: findById,
      findDadosCancelamento:findDadosCancelamento,
      buscarTiposAvaliacao:buscarTiposAvaliacao
    };

  }

})();

(function(){
  'use strict';

  angular.module('su-integrador')
    .controller('consultarFundamentoLegalController',
      ["$scope", "instrumentoFuncionalidadeService", "$state", "fundamentoLegalService", "integradorEscopoCompartilhadoService", function ($scope, instrumentoFuncionalidadeService, $state, fundamentoLegalService, integradorEscopoCompartilhadoService) {

        var vm = $scope;

        vm.listaInstrumentosFuncionalidade = [];
        vm.filtro = {};

        vm.listaFundamentosLegais = [];

        var init = function() {
          vm.registroInicial = 1;
          vm.tamanhoLimite = 5;
          vm.totalItems = 0;

          if (integradorEscopoCompartilhadoService.getObjeto('idInstrumentoFuncionalidade')
                && integradorEscopoCompartilhadoService.getObjeto('isEditar')) {
            vm.filtro.idInstrumentoFuncionalidade = integradorEscopoCompartilhadoService.getObjeto('idInstrumentoFuncionalidade');
            vm.isEditar = integradorEscopoCompartilhadoService.getObjeto('isEditar');
            integradorEscopoCompartilhadoService.limparEscopo();
          }

          buscarInstrumentosFuncionalidade();
          vm.pesquisarFundamentos();
        };

        var buscarInstrumentosFuncionalidade = function() {
          instrumentoFuncionalidadeService.buscarInstrumentosCadastradosAll().then(function(resp) {
            vm.listaInstrumentosFuncionalidade = resp.data.resposta;
          });
        };

        vm.redirecionarEditarFundamentoLegal = function (fundamento) {
          vm.isEditar = true;
          integradorEscopoCompartilhadoService.setObjetos('fundamentoLegal', fundamento);
          integradorEscopoCompartilhadoService.setObjetos('idInstrumentoFuncionalidade',fundamento.idInstrumento);
          integradorEscopoCompartilhadoService.setObjetos('isEditar',vm.isEditar);
          $state.go('INTEGRADOR.EDITAR_FUNDAMNETO_LEGAL');
        };

        vm.cancelarFundamentoLegal = function (fundamento) {
          integradorEscopoCompartilhadoService.setObjetos('fundamentoLegal', fundamento);
          $state.go('INTEGRADOR.CANCELAR_FUNDAMENTO_LEGAL');
        };

        vm.detalharFundamentoLegal = function (fundamento) {
          vm.isDetalhar = true;
          integradorEscopoCompartilhadoService.setObjetos('fundamentoLegal', fundamento);
          integradorEscopoCompartilhadoService.setObjetos('idInstrumentoFuncionalidade',fundamento.idInstrumento);
          integradorEscopoCompartilhadoService.setObjetos('isDetalhar',vm.isDetalhar);
          $state.go('INTEGRADOR.DETALHAR_FUNDAMENTO_LEGAL');
        };

        vm.voltar = function () {
          $state.go('INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE');
        };

        vm.limparFiltroConsulta = function() {
          vm.filtro = {};
          vm.filtro.idInstrumentoFuncionalidade = null;
        };

        vm.pesquisarFundamentos = function () {
          fundamentoLegalService.buscarFundamentosLegais(vm.filtro, (vm.registroInicial - 1), vm.tamanhoLimite).then(function(resp) {
            vm.listaFundamentosLegais = resp.data.resposta[0].content;
            vm.totalItems = resp.data.resposta[0].totalElements;
          });
        };

        vm.cadastrarFundamento = function () {
          if(vm.isEditar){
            integradorEscopoCompartilhadoService.setObjetos('idInstrumentoFuncionalidade',vm.filtro.idInstrumentoFuncionalidade);
            integradorEscopoCompartilhadoService.setObjetos('isEditar',vm.isEditar);
          }
          $state.go('INTEGRADOR.CADASTRAR_FUNDAMENTO_LEGAL');
        };

        init();
      }]);
})();

(function(){
  'use strict';

  angular.module('su-integrador')
    .controller('cadastrarFundamentoLegalController',
      ["$scope", "$filter", "instrumentoFuncionalidadeService", "fundamentoLegalService", "$state", "ComumFactory", "integradorEscopoCompartilhadoService", function ($scope, $filter, instrumentoFuncionalidadeService, fundamentoLegalService, $state, ComumFactory, integradorEscopoCompartilhadoService) {

        var vm = $scope;

        vm.fundamentoLegal = {naturezaJuridicaPermitidas: []};
        vm.bloquear = false;
        vm.valorPercentual = false;

        vm.listaInstrumentosFuncionalidade = [];
        vm.listaTiposAvaliacao = [];
        vm.listaCriterios = [];
        vm.listaNaturezasPermitidas = [];
        vm.listaFundamentosCadastrados = [];
        vm.listaIncorporacao = [];
        vm.listaAquisicao = [];
        vm.listaEntidadesExtintas = [];

        vm.registroInicial = 1;
        vm.tamanhoLimite = 5;
        vm.totalItems = 0;
        vm.paginationFundamentosLegaisTipoAvaliacao = [];

        vm.fundamentoLegalTipoAvaliacao = {};
        vm.fundamentoLegal.tipoAvaliacao = null;
        vm.fundamentoLegal.metodologiaAvaliacao = null;

        var TODAS = 'TODAS';
        var NORTE = 'NORTE';
        var NORDESTE = 'NORDESTE';
        var CENTRO_OESTE = 'CENTRO_OESTE';
        var SUDESTE = 'SUDESTE';
        var SUL = 'SUL';
        var URBANO = 'URBANO';
        var RURAL = 'RURAL';
        var edit = false;

        vm.DESTINACAO = 'DESTINACAO';
        vm.moduloSelecionado = null;

        var configuracoesPadrao = {
          regiao: '',
          regiaoLabel: '',
          natureza: '',
          naturezaLabel: '',
          isSuperintendente: true,
          isSecretario: true,
          isMinistro: true,
          minimoSuperintendente: 0,
          maximoSuperintendente: null,
          minimoSecretario: null,
          maximoSecretario: null,
          minimoMinistro: null,
          maximoMinistro: null,
          isDiferencaRegiao: false,
          isDiferencaNatureza: false
        };

        var todasLabel = $filter('translate')('label-todas');
        var regioesPadrao = {norte: NORTE, nordeste: NORDESTE, centrooeste: CENTRO_OESTE, sudeste: SUDESTE, sul: SUL};
        var regioesLabelPadrao = {norte: $filter('translate')('label-norte'), nordeste: $filter('translate')('label-nordeste'), centrooeste: $filter('translate')('label-centro-oeste'), sudeste: $filter('translate')('label-sudeste'), sul: $filter                ('translate')('label-sul')};
        var naturezasPadrao = {urbano: URBANO, rural: RURAL};
        var naturezasLabelPadrao = {urbano: $filter('translate')('label-urbano'), rural: $filter('translate')('label-rural')};

        vm.AREA_IMOVEL_PARCELA = 'AREA_IMOVEL_PARCELA';
        vm.VALOR_IMOVEL_PARCELA = 'VALOR_IMOVEL_PARCELA';

        var init = function() {
          vm.configuracoesTodasTodas = [
            definirConfiguracaoFundamentoLegal(TODAS, todasLabel, TODAS, todasLabel, false, false)
          ];
          vm.configuracoesTodasNatureza = [
            definirConfiguracaoFundamentoLegal(TODAS, todasLabel, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, false, true),
            definirConfiguracaoFundamentoLegal(TODAS, todasLabel, naturezasPadrao.rural, naturezasLabelPadrao.rural, false, true)
          ];
          vm.configuracoesRegiaoNatureza = [
            definirConfiguracaoFundamentoLegal(regioesPadrao.norte, regioesLabelPadrao.norte, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.norte, regioesLabelPadrao.norte, naturezasPadrao.rural, naturezasLabelPadrao.rural, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.nordeste, regioesLabelPadrao.nordeste, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.nordeste, regioesLabelPadrao.nordeste, naturezasPadrao.rural, naturezasLabelPadrao.rural, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.centrooeste, regioesLabelPadrao.centrooeste, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.centrooeste, regioesLabelPadrao.centrooeste, naturezasPadrao.rural, naturezasLabelPadrao.rural, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sudeste, regioesLabelPadrao.sudeste, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sudeste, regioesLabelPadrao.sudeste, naturezasPadrao.rural, naturezasLabelPadrao.rural, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sul, regioesLabelPadrao.sul, naturezasPadrao.urbano, naturezasLabelPadrao.urbano, true, true),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sul, regioesLabelPadrao.sul, naturezasPadrao.rural, naturezasLabelPadrao.rural, true, true)
          ];
          vm.configuracoesRegiaoTodas = [
            definirConfiguracaoFundamentoLegal(regioesPadrao.norte, regioesLabelPadrao.norte, TODAS, todasLabel, true, false),
            definirConfiguracaoFundamentoLegal(regioesPadrao.nordeste, regioesLabelPadrao.nordeste, TODAS, todasLabel, true, false),
            definirConfiguracaoFundamentoLegal(regioesPadrao.centrooeste, regioesLabelPadrao.centrooeste, TODAS, todasLabel, true, false),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sudeste, regioesLabelPadrao.sudeste, TODAS, todasLabel, true, false),
            definirConfiguracaoFundamentoLegal(regioesPadrao.sul, regioesLabelPadrao.sul, TODAS, todasLabel, true, false),
          ];

          if (integradorEscopoCompartilhadoService.getObjeto('idInstrumentoFuncionalidade')
            && (integradorEscopoCompartilhadoService.getObjeto('isEditar') || integradorEscopoCompartilhadoService.getObjeto('isDetalhar'))) {
            vm.fundamentoLegal.idInstrumentoFuncionalidade = integradorEscopoCompartilhadoService.getObjeto('idInstrumentoFuncionalidade');
            vm.isEditar = integradorEscopoCompartilhadoService.getObjeto('isEditar');
            vm.isDetalhar = integradorEscopoCompartilhadoService.getObjeto('isDetalhar');


            if(integradorEscopoCompartilhadoService.getObjeto('fundamentoLegal')){

              vm.fundamentoLegal = integradorEscopoCompartilhadoService.getObjeto('fundamentoLegal');
              edit = true;
              fundamentoLegalService.findById(vm.fundamentoLegal.idFundamento).then(function (resultado) {
                vm.fundamentoLegal = angular.copy(resultado.data.resposta[0]);
                vm.paginationFundamentosLegaisTipoAvaliacao = angular.copy(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList);
                vm.paginationFundamentosLegaisTipoAvaliacao = $filter('orderBy')(vm.paginationFundamentosLegaisTipoAvaliacao, 'ordem');
                integradorEscopoCompartilhadoService.limparEscopo();
                preencherConfiguracoesFundamentoLegal();
                if(vm.fundamentoLegal.utilizarFundamentoLegal){
                  vm.utilizarFundamento();
                }
                if(vm.isDetalhar){
                  $('[contenteditable]').attr('contenteditable', 'false');
                  $('[name=html]').attr('disabled', 'disabled');
                }
              });

            }
            if(!vm.fundamentoLegal.ativo && vm.fundamentoLegal.idFundamento){
              fundamentoLegalService.findDadosCancelamento(vm.fundamentoLegal.idFundamento).then(function (resultado) {
                vm.dadosCancelamento = angular.copy(resultado.data.resposta[0])
              })
            }
          }

          buscarInstrumentosFuncionalidade();
          buscarCriterios();
          buscarNaturezasPermitidas();
          buscarTodosFundamentos();
          buscarTiposIncorporacaoPermitidas();
          buscarTiposAquisicaoPermitidas();
          buscarEntidadesExtintas();
          buscarTiposAvaliacao();
        };

        var buscarInstrumentosFuncionalidade = function() {
          instrumentoFuncionalidadeService.buscarInstrumentosCadastradosAll().then(function(resp) {
            vm.listaInstrumentosFuncionalidade = resp.data.resposta;
          });
        };

        var buscarTiposAvaliacao = function() {
          fundamentoLegalService.buscarTiposAvaliacao().then(function(resp) {
            vm.listaTiposAvaliacao = resp.data.resposta;

            if(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList && vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList.length > 0){
              for(var i = 0; i < vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList.length; i++){
                for(var k = 0; k < vm.listaTiposAvaliacao.length; k++){
                  if(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList[i].tipoAvaliacao.id === vm.listaTiposAvaliacao[k].id){
                    vm.listaTiposAvaliacao.splice(k, 1);
                  }
                }
              }
            }
          });
        };

        var definirConfiguracaoFundamentoLegal = function (regiao, regiaoLabel, natureza, naturezaLabel, isDiferencaRegiao, isDiferencaNatureza) {
          var configuracao = angular.copy(configuracoesPadrao);
          configuracao.regiao = regiao;
          configuracao.regiaoLabel = regiaoLabel;
          configuracao.natureza = natureza;
          configuracao.naturezaLabel = naturezaLabel;
          configuracao.isDiferencaRegiao = isDiferencaRegiao;
          configuracao.isDiferencaNatureza = isDiferencaNatureza;
          return configuracao;
        };

        var buscarTodosFundamentos = function() {
          fundamentoLegalService.pesquisarTodosFundamentos().then(function(resp) {
            vm.listaFundamentosCadastrados = resp.data.resposta;
          });
        };

        var buscarCriterios = function() {
          fundamentoLegalService.buscarCriteriosUtilizado().then(function(resp) {
            vm.listaCriterios = resp.data.resposta;
          })
        };

        var buscarNaturezasPermitidas = function() {
          fundamentoLegalService.buscarNaturezasPermitidas().then(function(resp) {
            vm.listaNaturezasPermitidas = resp.data.resposta;
          });
        };

        var buscarTiposIncorporacaoPermitidas = function() {
          fundamentoLegalService.pesquisarTodosTiposIncorporacao().then(function(resp) {
            vm.listaIncorporacao = resp.data.resposta;
          });
        };

        var buscarTiposAquisicaoPermitidas = function() {
          fundamentoLegalService.pesquisarTodosTiposAquisicao().then(function(resp) {
            vm.listaAquisicao = resp.data.resposta;
          });
        };

        var buscarEntidadesExtintas = function() {
          fundamentoLegalService.pesquisarTodasEntidadesExtintas().then(function(resp) {
            vm.listaEntidadesExtintas = resp.data.resposta;
          });
        };

        $scope.$watch('fundamentoLegal.idInstrumentoFuncionalidade', function() {
          instrumentoFuncionalidadeService.buscarInstrumentosCadastradosAll().then(function(resp) {
            vm.listaInstrumentosFuncionalidade = resp.data.resposta;
            angular.forEach(vm.listaInstrumentosFuncionalidade, function(instrumentoFunc) {
              if (vm.fundamentoLegal.idInstrumentoFuncionalidade===instrumentoFunc.instrumentoFuncionalidade.id) {
                vm.moduloSelecionado = instrumentoFunc.instrumentoFuncionalidade.modulo.constante;
              }
            });
          });
        });

        vm.incrementarAreaValor = function(valor) {
          return valor + 0.01;
        };

        vm.fecharFundamentoLegal = function() {
          $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
        };

        var validarConfiguracoesFundamentoLegal = function() {
          if (vm.fundamentoLegal.isDiferencaRegiao==vm.configuracoesTodasTodas[0].isDiferencaRegiao
            && vm.fundamentoLegal.isDiferencaNatureza==vm.configuracoesTodasTodas[0].isDiferencaNatureza) {
            vm.fundamentoLegal.configuracoes = vm.configuracoesTodasTodas;
          } else if (vm.fundamentoLegal.isDiferencaRegiao==vm.configuracoesTodasNatureza[0].isDiferencaRegiao
            && vm.fundamentoLegal.isDiferencaNatureza==vm.configuracoesTodasNatureza[0].isDiferencaNatureza) {
            vm.fundamentoLegal.configuracoes = vm.configuracoesTodasNatureza;
          } else if (vm.fundamentoLegal.isDiferencaRegiao==vm.configuracoesRegiaoNatureza[0].isDiferencaRegiao
            && vm.fundamentoLegal.isDiferencaNatureza==vm.configuracoesRegiaoNatureza[0].isDiferencaNatureza) {
            vm.fundamentoLegal.configuracoes = vm.configuracoesRegiaoNatureza;
          } else if (vm.fundamentoLegal.isDiferencaRegiao==vm.configuracoesRegiaoTodas[0].isDiferencaRegiao
            && vm.fundamentoLegal.isDiferencaNatureza==vm.configuracoesRegiaoTodas[0].isDiferencaNatureza) {
            vm.fundamentoLegal.configuracoes = vm.configuracoesRegiaoTodas;
          }
        };

        vm.salvarFundamentoLegal = function(formularioDadosFundamentoLegal) {
          if(formularioDadosFundamentoLegal.$invalid){
            formularioDadosFundamentoLegal.$setSubmitted();
            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
            return;
          }

          validarConfiguracoesFundamentoLegal();

          fundamentoLegalService.salvar(vm.fundamentoLegal).then(function() {
            var mensagem;
            if(vm.isEditar){
              mensagem = 'Dados alterados com sucesso'
            }else{
              mensagem = 'Dados gravados com sucesso'
            }
            ComumFactory.mostrarMensagemSucesso(mensagem);
            $state.go('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL');
          });
        };

        vm.utilizarFundamento = function () {
          if(vm.fundamentoLegal.utilizarFundamentoLegal){
            fundamentoLegalService.findById(vm.fundamentoLegal.utilizarFundamentoLegal.id).then(function (resultado) {
              vm.fundamentoLegal2 = angular.copy(resultado.data.resposta[0]);
              for(var attr in vm.fundamentoLegal2){
                if(vm.fundamentoLegal2.hasOwnProperty(attr)) {
                  if(attr !== 'id' && attr !== 'template' && attr !== 'descricao' && attr !== 'idInstrumentoFuncionalidade' && attr !== 'utilizarFundamentoLegal'){
                    vm.fundamentoLegal[attr] = vm.fundamentoLegal2[attr];
                  }
                }
              }
              preencherConfiguracoesFundamentoLegal();
            });
            vm.bloquear = true;
          }else {
            for(var attr in vm.fundamentoLegal){
              if(vm.fundamentoLegal.hasOwnProperty(attr)) {
                if(attr !== 'id' && attr !== 'template' && attr !== 'descricao' && attr !== 'idInstrumentoFuncionalidade' && attr !== 'utilizarFundamentoLegal'){
                  if(attr !== 'utilizacoes'){
                    vm.fundamentoLegal[attr] = null;
                  }else{
                    vm.fundamentoLegal.utilizacoes = [];
                  }
                }
              }
            }
            vm.bloquear = false;
          }
        };

        vm.validarCriterioUtilizado = function () {

          return vm.fundamentoLegal.isExigirAtoAutorizativo && ((vm.fundamentoLegal.isSuperintendente && vm.fundamentoLegal.isSecretario) ||
                                                                (vm.fundamentoLegal.isMinistro && vm.fundamentoLegal.isSuperintendente) ||
                                                                (vm.fundamentoLegal.isMinistro && vm.fundamentoLegal.isSecretario));

        };

        var preencherConfiguracoesFundamentoLegal = function () {
          if (vm.fundamentoLegal.isDiferencaRegiao == false
            && vm.fundamentoLegal.isDiferencaNatureza == false) {
            for (var posicao in vm.fundamentoLegal.configuracoes){
              for(var attr in vm.fundamentoLegal.configuracoes[posicao]){
                if(vm.fundamentoLegal.configuracoes[posicao].hasOwnProperty(attr)) {
                  if((attr !== 'regiaoLabel' && attr !== 'naturezaLabel') || (attr === 'id' && vm.fundamentoLegal.id)){
                    vm.configuracoesTodasTodas[posicao][attr] = vm.fundamentoLegal.configuracoes[posicao][attr];
                  }
                }
              }
            }
          } else if (vm.fundamentoLegal.isDiferencaRegiao == false
            && vm.fundamentoLegal.isDiferencaNatureza==true) {
            for (var posicao in vm.fundamentoLegal.configuracoes){
              for(var attr in vm.fundamentoLegal.configuracoes[posicao]){
                if(vm.fundamentoLegal.configuracoes[posicao].hasOwnProperty(attr)) {
                  if((attr !== 'regiaoLabel' && attr !== 'naturezaLabel') || (attr === 'id' && vm.fundamentoLegal.id) ){
                    vm.configuracoesTodasNatureza[posicao][attr] = vm.fundamentoLegal.configuracoes[posicao][attr];
                  }
                }
              }
            }
          } else if (vm.fundamentoLegal.isDiferencaRegiao==true
            && vm.fundamentoLegal.isDiferencaNatureza==true) {
            for (var posicao in vm.fundamentoLegal.configuracoes){
              for(var attr in vm.fundamentoLegal.configuracoes[posicao]){
                if(vm.fundamentoLegal.configuracoes[posicao].hasOwnProperty(attr)) {
                  if((attr !== 'regiaoLabel' && attr !== 'naturezaLabel') || (attr === 'id' && vm.fundamentoLegal.id)){
                    vm.configuracoesRegiaoNatureza[posicao][attr] = vm.fundamentoLegal.configuracoes[posicao][attr];
                  }
                }
              }
            }
          } else if (vm.fundamentoLegal.isDiferencaRegiao==true
            && vm.fundamentoLegal.isDiferencaNatureza==false) {
            for (var posicao in vm.fundamentoLegal.configuracoes){
              for(var attr in vm.fundamentoLegal.configuracoes[posicao]){
                if(vm.fundamentoLegal.configuracoes[posicao].hasOwnProperty(attr)) {
                  if((attr !== 'regiaoLabel' && attr !== 'naturezaLabel') || (attr === 'id' && vm.fundamentoLegal.id)){
                    vm.configuracoesRegiaoTodas[posicao][attr] = vm.fundamentoLegal.configuracoes[posicao][attr];
                  }
                }
              }
            }
          }
        };

        vm.mostrarTipoAquisicao = function () {
          var b = false;
          for(var index in vm.fundamentoLegal.formaDeIncorporacaoPermitidas) {
            if(vm.fundamentoLegal.formaDeIncorporacaoPermitidas[index].descricao === 'Por Aquisição'){
              b = true;
            }
          }
          return b;
        };

        vm.mostrarEntidadeExtinta = function () {
          var b = false;
          for(var index in vm.fundamentoLegal.tipoAquisicaoPermitidas) {
            if(vm.fundamentoLegal.tipoAquisicaoPermitidas[index].descricao === 'Sucessão por Entidades da APF'){
              b = true;
            }
          }
          return b;
        };

        vm.selecionarTodosResponsavel = function (parametro) {
          if(parametro){
            vm.fundamentoLegal.naturezaJuridicaPermitidas = vm.listaNaturezasPermitidas;
          } else{
            vm.fundamentoLegal.naturezaJuridicaPermitidas = [];
          }
        };

        vm.selecionarTodasFormas = function (parametro) {
          if(parametro){
            vm.fundamentoLegal.formaDeIncorporacaoPermitidas = vm.listaIncorporacao;
          } else{
            vm.fundamentoLegal.formaDeIncorporacaoPermitidas = [];
          }
        };

        vm.selecionarTodasTiposAquisicao = function (parametro) {
          if(parametro){
            vm.fundamentoLegal.tipoAquisicaoPermitidas = vm.listaAquisicao;
          } else{
            vm.fundamentoLegal.tipoAquisicaoPermitidas = [];
          }
        };

        vm.selecionarTodasEntidadesExtintas = function (parametro) {
          if(parametro){
            vm.fundamentoLegal.entidadeExtintas = vm.listaEntidadesExtintas;
          } else{
            vm.fundamentoLegal.entidadeExtintas = [];
          }
        };

        vm.adicionarTipoAvaliacao = function(tipoAvaliacao){
          if(!tipoAvaliacao){
            return;
          }
          !vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList ? vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList = [] : "";

          vm.fundamentoLegalTipoAvaliacao.tipoAvaliacao = {};
          vm.fundamentoLegalTipoAvaliacao.ordem = null;
          vm.fundamentoLegalTipoAvaliacao.tipoAvaliacao = angular.copy(tipoAvaliacao);

          vm.paginationFundamentosLegaisTipoAvaliacao.length > 0 ? vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList = angular.copy(vm.paginationFundamentosLegaisTipoAvaliacao) : "";

          vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList.push(angular.copy(vm.fundamentoLegalTipoAvaliacao));

          if(vm.fundamentoLegal.metodologiaAvaliacao === true){
            for(var i = 0; i < vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList.length; i++){
              if(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList[i].ordem == null){
                vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList[i].ordem = i + 1;
              }
            }

            vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList = $filter('orderBy')(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList, 'ordem');
          }

          for(var k = 0; k < vm.listaTiposAvaliacao.length; k++){
            if(vm.listaTiposAvaliacao[k].id === tipoAvaliacao.id){
              vm.listaTiposAvaliacao.splice(k, 1);
            }
          }

          vm.paginacaoTipoAvaliacao();
          vm.fundamentoLegal.tipoAvaliacao = null;
        };

         vm.removerTipoAvaliacao = function(index){
           for(var i = 0; i < vm.paginationFundamentosLegaisTipoAvaliacao.length; i++){
             if(i === index){
               vm.listaTiposAvaliacao.push(vm.paginationFundamentosLegaisTipoAvaliacao[i].tipoAvaliacao);
               vm.paginationFundamentosLegaisTipoAvaliacao.splice(i, 1);
             }
           }

           vm.ordenarArray();
         };

         vm.ordenarArray = function(){
           for(var i = 0; i < vm.paginationFundamentosLegaisTipoAvaliacao.length; i++){
             vm.paginationFundamentosLegaisTipoAvaliacao[i].ordem = i + 1;
           }

           vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList = angular.copy(vm.paginationFundamentosLegaisTipoAvaliacao);
         };

        vm.incrementarOrdem = function (arrayFundamentoLegalTipoAvaliacao, ordem) {
          for (var i = 0; i < arrayFundamentoLegalTipoAvaliacao.length; i++) {
            if (arrayFundamentoLegalTipoAvaliacao[i].ordem === ordem) {
              arrayFundamentoLegalTipoAvaliacao[i].ordem = arrayFundamentoLegalTipoAvaliacao[i].ordem + 1;
            } else if (arrayFundamentoLegalTipoAvaliacao[i].ordem === (ordem + 1)) {
              arrayFundamentoLegalTipoAvaliacao[i].ordem = arrayFundamentoLegalTipoAvaliacao[i].ordem - 1;
            }
          }

          vm.paginationFundamentosLegaisTipoAvaliacao = $filter('orderBy')(vm.paginationFundamentosLegaisTipoAvaliacao, 'ordem');
        };

        vm.decrementarOrdem = function (arrayFundamentoLegalTipoAvaliacao, ordem) {
          for (var i = 0; i < arrayFundamentoLegalTipoAvaliacao.length; i++) {
            if (arrayFundamentoLegalTipoAvaliacao[i].ordem === ordem) {
              arrayFundamentoLegalTipoAvaliacao[i].ordem = arrayFundamentoLegalTipoAvaliacao[i].ordem - 1;

            } else if (arrayFundamentoLegalTipoAvaliacao[i].ordem === (ordem - 1)) {
              arrayFundamentoLegalTipoAvaliacao[i].ordem = arrayFundamentoLegalTipoAvaliacao[i].ordem + 1;
            }
          }

          vm.paginationFundamentosLegaisTipoAvaliacao = $filter('orderBy')(vm.paginationFundamentosLegaisTipoAvaliacao, 'ordem');
        };

        vm.paginacaoTipoAvaliacao = function (nextPage, novoTamanhoLimite) {
          vm.paginationFundamentosLegaisTipoAvaliacao = [];
          vm.totalItems = angular.copy(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList.length);
          novoTamanhoLimite ? vm.tamanhoLimite = novoTamanhoLimite :"";

          if (vm.totalItems < vm.tamanhoLimite) {
            vm.paginationFundamentosLegaisTipoAvaliacao = angular.copy(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList);
          } else {
            for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
              vm.paginationFundamentosLegaisTipoAvaliacao.push(angular.copy(vm.fundamentoLegal.fundamentoLegalTipoAvaliacaoList[i]));
            }
          }
        };

        vm.validaPercentual = function(percentual){
          if(percentual == 0 || percentual > 100){
            ComumFactory.mostrarMensagemError($filter('translate')('erro-percentual-invalido'));
            vm.fundamentoLegal.percentual = null;
          }
        };

        init();
      }]);
})();

(function () {
  'use strict';

  angular
    .module('su-integrador')
    .factory('documentosFactory',
      ["comumIntegradorService", "$http", "ComumFactory", function (comumIntegradorService, $http, ComumFactory) {

        var URL_DOCUMENTO_PESSOA = "/integrador/api/documento";

        var URL_INTEGRADOR_COMUM = "/integrador/api/comum";

        var getFormatoArquivoSelecionado = function (novoDocumento, listaFormatosArquivos) {
          novoDocumento.formatoArquivos = [];

          angular.forEach(listaFormatosArquivos, function (formatoDoc) {
            if(formatoDoc.selecionado){
              novoDocumento.formatoArquivos.push(formatoDoc);

            }
          })
        };

        var limparFormatosSelecionados = function (listaFormatosArquivos, callback) {
          angular.forEach(listaFormatosArquivos, function (doc) {
            doc.selecionado = false;

          });

          callback;

        };

        var findExtensao = function (arrayFormatos, extensao) {
          for (var i = 0; i < arrayFormatos.length; i++) {
            if (extensao == arrayFormatos[i].formato)
              return true;
          }
          return false;
        };

        var selecionarCheckboxsFormatosArq = function (novoDocumento,listaFormatosArquivos ) {
          angular.forEach(novoDocumento.formatoArquivos, function (formatoSelecionado) {
            angular.forEach(listaFormatosArquivos, function (formatoArray) {
              if (formatoSelecionado.id == formatoArray.id) {
                formatoArray.selecionado = true;
              }
            });
          });
        };

        var addDocumento = function ($event, form, documentos, novoDocumento,
                                     statusExtensaoDocumento, outraExtensaoDocumento,
                                     listaFormatosArquivos, itemOld, isEdicao, callback) {

          form.$submitted = true;
          var extensao = '';

          getFormatoArquivoSelecionado(novoDocumento, listaFormatosArquivos);

          if (statusExtensaoDocumento && outraExtensaoDocumento != "") {
            extensao = outraExtensaoDocumento.replace(/[\W]+/g, "").toUpperCase();

            var resp = findExtensao(listaFormatosArquivos, extensao);

            if (resp) {
              ComumFactory.mostrarMensagemError('O formato de extensão informado já existe.');
              return;
            } else {
              novoDocumento.formatoArquivos.push({
                'formato': extensao
              });
            }
          }

          $event.preventDefault();


          if (isEdicao) {

            for(var i=0; i<documentos.length;i++){

              if (documentos[i].nome == itemOld.nome) {

                documentos[i] = angular.copy(novoDocumento);
              }
            }

          } else {
            novoDocumento.status = true;
            documentos.push(novoDocumento);
          }

          callback();

        };

        var buscarDocumentosPessoa = function(filtroConsulta, offset, limit) {
          return $http.get(URL_DOCUMENTO_PESSOA, {
            params: {
              nome: filtroConsulta.nome,
              pessoaFisica: filtroConsulta.pessoaFisica,
              pessoaJuridica: filtroConsulta.pessoaJuridica,
              imovel: filtroConsulta.imovel,
              situacao: filtroConsulta.situacao,
              offset: offset,
              limit: limit
            }
          });
        };

        var consultarDocumentoPessoa = function(idDocumento) {
          return $http.get(URL_DOCUMENTO_PESSOA + '/consultar/' + idDocumento);
        };

        var listarFormatosArquivo = function () {
          return $http.get(URL_INTEGRADOR_COMUM + '/formato-arquivo/');
        };

        var salvar =function (data) {
          return $http.post(URL_DOCUMENTO_PESSOA + '/salvar/', data);
        };

        return {
          getFormatoArquivoSelecionado: getFormatoArquivoSelecionado,
          limparFormatosSelecionados: limparFormatosSelecionados,
          addDocumento: addDocumento,
          selecionarCheckboxsFormatosArq: selecionarCheckboxsFormatosArq,
          buscarDocumentosPessoa: buscarDocumentosPessoa,
          consultarDocumentoPessoa: consultarDocumentoPessoa,
          listarFormatosArquivo: listarFormatosArquivo,
          salvar: salvar
        };

      }])

}());

(function () {

  'use strict';
  angular
    .module('su-integrador')
    .controller('consultarDocumentoPessoaController',
      ["$scope", "$state", "documentosFactory", "integradorEscopoCompartilhadoService", function ($scope, $state, documentosFactory, integradorEscopoCompartilhadoService) {

        var vm = $scope;

        vm.filtroConsulta = {};

        var init = function () {
          vm.registroInicial = 1;
          vm.tamanhoLimite = 5;
          vm.totalItems = 0;

          vm.listagemDocumentosPessoa = [];
          vm.paginadosDocumentosPessoa = [];
          vm.consultarDocumentosPessoa();
        };

        vm.consultarDocumentosPessoa = function() {
          documentosFactory.buscarDocumentosPessoa(vm.filtroConsulta)
            .then(function(resp) {
              vm.listagemDocumentosPessoa = resp.data.resposta;
              vm.totalItems = resp.data.resposta.length;
              vm.paginarDocumentosPessoa();
            });
        };

        vm.redirecionarCadastrarDocumentosPessoa = function() {
          $state.go('INTEGRADOR.CADASTRO_DOCUMENTO');
        };

        vm.limparConsulta = function() {
          vm.filtroConsulta = {};
        };

        vm.exibirFormatosArquivoDocumento = function(documento) {
          if (documento.formatoArquivos) {
            var cont = 0;
            var labelFormatos = '';
            angular.forEach(documento.formatoArquivos,  function(formatoArquivo){
              if (cont > 0) {
                labelFormatos += ', ';
              }
              labelFormatos += '.' + formatoArquivo.formato.toLowerCase();
              cont++;
            });
            return labelFormatos;
          }
        };

        vm.exibirOutrosFormatos = function (documento) {
          var labelOutrosDocumentos ='';
          if(documento.outro !== null){
          labelOutrosDocumentos += '.'+ documento.outro;
          return labelOutrosDocumentos;
          }
        };



        vm.validarSituacaoDocumento = function(situacao) {
          if (situacao) {
            return 'ativo';
          } else {
            return 'inativo';
          }
        };

        vm.paginarDocumentosPessoa = function() {
          vm.paginadosDocumentosPessoa = [];
          vm.totalItems = vm.listagemDocumentosPessoa.length;
          if (vm.totalItems < vm.tamanhoLimite) {
            vm.paginadosDocumentosPessoa = vm.listagemDocumentosPessoa;
          } else {
            for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
              vm.paginadosDocumentosPessoa.push(vm.listagemDocumentosPessoa[i]);
              if (vm.paginadosDocumentosPessoa.length == vm.tamanhoLimite) {
                break;
              }
            }
          }
        };

        vm.redirecionarEditarDocumentoPessoa = function(idDocumento) {
          integradorEscopoCompartilhadoService.setObjetos('idDocumento', idDocumento);
          $state.go('INTEGRADOR.EDITAR_DOCUMENTO');
        };

        init();
      }])
})();



(function () {

  'use strict';
  angular
    .module('su-integrador')
    .controller('cadastroDocumentoController',
      ["$scope", "$stateParams", "documentosFactory", "$state", "$rootScope", "$timeout", "$q", "$filter", "ComumFactory", "formularioIntegradorService", "integradorEscopoCompartilhadoService", function ($scope, $stateParams, documentosFactory, $state, $rootScope,
                $timeout, $q, $filter, ComumFactory, formularioIntegradorService, integradorEscopoCompartilhadoService) {

        var vm = $scope;
        var validaFormulario = formularioIntegradorService.getInstance();
        vm.validarFormulario = validaFormulario.validarFormulario;
        vm.isEditar = false;

        var init = function () {
          vm.listaFormatosArquivos = [];
          carregarFormatosArquivo();

          if (integradorEscopoCompartilhadoService.getObjeto('idDocumento')) {
            var idDocumento = integradorEscopoCompartilhadoService.getObjeto('idDocumento');
            documentosFactory.consultarDocumentoPessoa(idDocumento).then(function (resp) {
              vm.documento = resp.data.resultado;
              vm.statusExtensaoDocumento = vm.documento.outro ? true : false;
              angular.forEach(vm.listaFormatosArquivos, function (formatoArquivo) {
                angular.forEach(vm.documento.formatoArquivos, function (formatoDocumento) {
                  if (formatoArquivo.id == formatoDocumento.id) {
                    formatoArquivo.selecionado = true;
                  }
                })
              });
            });
            vm.isEditar = true;
            integradorEscopoCompartilhadoService.limparEscopo();
          } else {
            vm.documento = {
              documentos: [],
              formatoArquivos: [],
              pessoaFisica: false,
              pessoaJuridica: false,
              imovel: false
            };
          }

          vm.listas = {
            documento: []

          };

          vm.novoDocumento = {};
          vm.formatoArquivos = [];
          montarListaDocumento();
        };
        init();

        function carregarFormatosArquivo() {
          documentosFactory.listarFormatosArquivo().then(function (resposta) {
            vm.listaFormatosArquivos = resposta.data.resposta;
          });
        }

        var limparFormatos = function () {
          documentosFactory.limparFormatosSelecionados(vm.listaFormatosArquivos,
            function () {
              vm.statusExtensaoDocumento = false;
              vm.outraExtensaoDocumento = "";
            });
        };

        vm.addDocumento = function ($event) {

          documentosFactory.addDocumento(
            $event, vm.adicionarDocumentos, vm.documentos,
            vm.novoDocumento, vm.statusExtensaoDocumento,
            vm.outraExtensaoDocumento, vm.listaFormatosArquivos,
            function () {

              vm.novoDocumento = {
                nome: undefined
              };

              limparFormatos();
              vm.adicionarDocumentos.$submitted = false;
            });
        };
        vm.limparCampos = function () {
          vm.id = vm.documento.id;
          vm.pessoaFisica = vm.documento.pessoaFisica;
          vm.pessoaJuridica = vm.documento.pessoaJuridica;
          vm.imovel = vm.documento.imovel;
          vm.documento = {};
          vm.statusExtensaoDocumento = false;
          vm.listas = {};
          init();
          vm.documento.id = vm.id;
          vm.documento.pessoaFisica = vm.pessoaFisica;
          vm.documento.pessoaJuridica = vm.pessoaJuridica;
          vm.documento.imovel = vm.imovel;
        };

        vm.fechar = function () {
          $state.go('INTEGRADOR.CONSULTAR_DOCUMENTO_PESSOA');
        };

        function salvarDocumento() {
          vm.documento.formatoArquivos = getExtensoesSelecionadas();
          documentosFactory.salvar(vm.documento).then(function (resposta) {
            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-documentos'))
            $state.go('INTEGRADOR.CONSULTAR_DOCUMENTO_PESSOA');
          });

        }

        vm.validarFormulario = function () {
          if (vm.formCadastroDocumentos.$valid && validarVinculo() && validarExtensoes()) {
            salvarDocumento();
          } else {
            vm.formCadastroDocumentos.$setSubmitted();
            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
          }
        };

        function getExtensoesSelecionadas() {
          var formatosSelecionado = [];
          angular.forEach(vm.listaFormatosArquivos, function (formato) {
            if (formato.selecionado) {
              formatosSelecionado.push(formato);
            }
          });
          return formatosSelecionado;
        }

        function validarVinculo() {
          return vm.documento.pessoaFisica || vm.documento.pessoaJuridica || vm.documento.imovel;
        }

        function validarExtensoes() {
          return getExtensoesSelecionadas().length > 0 || vm.statusExtensaoDocumento;
        }

        function montarListaDocumento () {
        angular.forEach(vm.listas.documento, function (item) {
          if (item.id == undefined || item.id == 0) {
            if (!vm.documento.documentos) {
              vm.documento.documentos = [];
            }
            vm.documento.documentos.push(item);
          } else {

            for (var i = 0; i < vm.documento.documentos.length; i++) {
              if (item.id == vm.documento.documentos[i].id) {
                vm.documento.documentos[i] = angular.copy(item);
                return;
              }
            }
          }
        });
      }
      }])
})();



(function () {

  'use strict';

  service.$inject = ["$http"];
  angular
    .module('su-integrador')
    .factory('consultaOrientacaoTecnicaService', service);

  function service ($http) {

    var url = "integrador/api/orientacaoTecnica";

    var pesquisar = function(filtro){
      return $http.get(url + "/pesquisaOrientacaoTecnica/?"+generateFilter(filtro)+"areas=piru,rola,pinto,piru");
    };

    var generateFilter = function(filtro){
      var string = "";
      for(var key in filtro){
        if(filtro.hasOwnProperty(key)){
          string += key+'='+filtro[key]+'&';
        }
      }
      return string;
    };

    return {
      pesquisar: pesquisar
    }

  }

})();

(function () {
  'use strict';
  angular
    .module('su-integrador')
    .controller('consultaOrientacaoTecnicaController',
      ['$scope', '$element', 'ComumFactory', '$mdDialog', 'consultaOrientacaoTecnicaService', '$state', 'integradorEscopoCompartilhadoService', 'orientacaoTecnicaService',
        function ($scope, $element, ComumFactory, $mdDialog, consultaOrientacaoTecnicaService, $state, integradorEscopoCompartilhadoService, orientacaoTecnicaService) {

          var vm = $scope;

          vm.listaOrientacaoTecnica = [];

          vm.filtro = {
            offset: 0,
            limit: 5
          };

          $element.find('input').on('keydown', function(ev){
            ev.stopPropagation();
          });

          var init = function(){
            vm.pesquisar();

            orientacaoTecnicaService.getAreasOrientacaoTecnica().then(function(resp){
              vm.areasOrientacaoTecnica = resp.data.resposta;
            });

            orientacaoTecnicaService.getAssuntosOrientacaoTecnica().then(function(resp){
              vm.assuntosOrientacaoTecnica = resp.data.resposta;
            });
          };

          vm.clearFilter = function(){
            vm.filtro = {
              offset: 0,
              limit: 5
            };
          };

          vm.novoCadastro = function(){
            $state.go('INTEGRADOR.CADASTRO_ORIENTACAO_TECNICA');
          };

          vm.clearSearchAssunto = function(){
            vm.searchAssunto = "";
          };

          vm.clearSearchArea = function(){
            vm.searchArea = "";
          };

          vm.pesquisar = function(){
            consultaOrientacaoTecnicaService.pesquisar(vm.filtro).then(function(resp){
              vm.listaOrientacaoTecnica = resp.data.resposta[0].content;
            });
          };

          init();

        }]);
})();

(function(){
    'use strict';

        angular
        .module('su-integrador')
        .service('atosContratosService', ['$http', '$filter', 'comumIntegradorService', service]);

        function service($http, $filter, comumIntegradorService){

            var urlComum = 'integrador/api/comum';
            var URL_ATOS_CONTRATOS = 'integrador/api/atos-contratos';

            var GESTAO_PRAIA = 2;

            var buscarTipoModeloContrato = function(){
                return $http.get(urlComum + '/pesquisar-tipo-modelo-contratos');
            };

            var buscarTipoCompetenciaAssinatura = function(){
                return $http.get(urlComum + '/pesquisar-tipo-competencia-assinatura');
            };

            var buscarModulos = function(){
                return $http.get('integrador/api/modulos');
            };

            var buscarFuncionalidadePorModulo = function(id){
                return $http.get('integrador/api/instrumento-funcionalidade/pesquisar-por-modulo/' + id);
            };

            var buscarAlterarAtoContrato = function(idContrato) {
              return $http.get(URL_ATOS_CONTRATOS + '/buscar-entidades/' + idContrato)
            }

            var buscarAtoContrato = function(idContrato) {
                return $http.get(URL_ATOS_CONTRATOS + '/buscar/' + idContrato)
            };

            var buscarDespachoPorFuncionalidade = function (id) {
                return $http.get('integrador/api/instrumento-funcionalidade/pesquisa-despacho-por-funcionalidade/' + id);
            };

            var excluirModeloAtosContratos = function(atosContratos) {
                return $http.post(URL_ATOS_CONTRATOS + '/deletar', atosContratos);
            };

            var salvar = function(data){
                return $http.post(URL_ATOS_CONTRATOS + '/salvar', data);
            };

            var buscarCodPrefixo = function (prefixo) {
                return $http.get(URL_ATOS_CONTRATOS + '/gerar-prefixo/'+ prefixo);
            };

            var buscarFundamentoLegalFuncionalidade = function (id) {
                return $http.get('integrador/api/fundamento-legal/pesquisar-fundamento-legal/' + id);
            }

            // var buscarFundamentoLegalPorTipo = function (tipo) {
            //     return $http.get('integrador/api/fundamento-legal/pesquisar-por-tipo/' + tipo);
            // };

            var selecionarTodos = function (principal) {
              principal.status = !principal.status;

                angular.forEach(principal.item, function (item) {
                  item.status = principal.status;
                });
            };

            var buscarTodosAtosContratos = function(offset, limit) {
                return $http.get(URL_ATOS_CONTRATOS, {
                  params: {
                    offset: offset,
                    limit: limit
                  }
                });
            };

            var filterBuscarTodosAtosContratos = function(filtro, offset, limit) {
              return $http.get(URL_ATOS_CONTRATOS, {
                params: {
                  descricao: filtro.descricao,
                  modulo: filtro.modulo,
                  tipoModeloContrato: filtro.tipoModeloContrato,
                  instrumentoFuncionalidade: filtro.instrumentoFuncionalidade,
                  offset: offset,
                  limit: limit
                }
              });
            };

            var getConfiguracaoInicialContratoTermo = function (tipo) {
              return [{
                        status: true,
                        ordem: 0,
                        item: [{descricao: 'QUALIFICAÇÃO DAS PARTES', status: true, hierarquia: 'PRIMARIO', identificador: 'QUALIFICACAO_DAS_PARTES'},
                               {
                                 descricao: undefined,
                                 status: false,
                                 hierarquia: 'SECUNDARIO',
                                 identificador: 'DENOMINACAO_UNIAO',
                                 label: undefined,
                                 item: [{descricao: '[nome de quem assina], [cargo], CPF', status: true, hierarquia: 'TERCIARIO', identificador: 'DADOS_DA_PESSOA'}]
                               },
                               {
                                 descricao: undefined,
                                 status: false,
                                 hierarquia: 'SECUNDARIO',
                                 identificador: 'DENOMINACAO_TERCEIRO',
                                 item: [{descricao: '[Nome da pessoa]', status: true, hierarquia: 'TERCIARIO', identificador: 'NOME_PESSOA'},
                                        {descricao: 'Endereço', status: true, hierarquia: 'TERCIARIO', identificador: 'ENDERECO_MUNICIPIO'},
                                        {descricao: 'Endereço', status: true, hierarquia: 'TERCIARIO', identificador: 'ENDERECO_PREFEITO'}]
                               }]
                      },{
                        status: true,
                        ordem: 1,
                        item: [{descricao: 'IMÓVEL', status: true, hierarquia: 'PRIMARIO', label: undefined, identificador: 'IMOVEL'},
                                {descricao:'RIP', status: true, hierarquia: 'SECUNDARIO', identificador: 'RIP'},
                                {descricao:'ENDEREÇO', status: true, hierarquia: 'SECUNDARIO', identificador: 'ENDERECO'},
                                {descricao:'ÁREA DO TERRENO', status: true, hierarquia: 'SECUNDARIO', identificador: 'AREA_TERRENO_CONTRATO'},
                                {descricao:'NATUREZA', status: true, hierarquia: 'SECUNDARIO', identificador: 'NATUREZA_CONTRATO'},
                                {descricao:'MATRÍCULA CARTORIAL', status: true, hierarquia: 'SECUNDARIO', identificador: 'MATRICULA_CARTORIAL'},
                                {descricao:'MEMORIAL DESCRITIVO', status: true, hierarquia: 'SECUNDARIO', identificador: 'MEMORIAL_DESCRITIVO'}]
                    },{
                        status: true,
                        ordem: 2,
                        item: [{descricao: 'PRAZO', status: true, hierarquia: 'PRIMARIO', label: '[traz o prazo definido no instrumento/funcionalidade]', identificador: 'PRAZO_CONTRATO'}]
                    },{
                        status: true,
                        ordem: 3,
                        item: [{descricao: 'FINALIDADE', status: true, hierarquia: 'PRIMARIO', identificador: 'FINALIDADE_CONTRATO'},
                               {descricao:'TIPO', status: true, hierarquia: 'SECUNDARIO', identificador: 'TIPO_CONTRATO'},
                               {descricao:'ESPECIFICAÇÃO', status: true, hierarquia: 'SECUNDARIO', identificador: 'ESPECIFICACAO_CONTRATO'},
                               {descricao:'DESCRIÇÃO', status: true, hierarquia: 'SECUNDARIO', identificador: 'DESCRICAO_CONTRATO'}]
                    },
                    {
                        status: true,
                        ordem: 4,
                        item: [{descricao: 'ENCARGO(S)', status: true, hierarquia: 'PRIMARIO', label: '[traz os encargos definid]', identificador: 'ENCARGOS_CONTRATO'}]
                    },{
                        status: true,
                        ordem: 5,
                        item: [{descricao: 'PREÇOS E CONDIÇÕES DE PAGAMENTO', status: true, hierarquia: 'PRIMARIO', identificador: 'PRECOS_CONDICOES_PAGAMENTO_CONTRATO'}]
                    },{
                        status: true,
                        ordem: 6,
                        item: [{descricao: 'CONDIÇÕES', status: true, hierarquia: 'PRIMARIO', identificador: 'CONDICOES_CONTRATO'}]
                    }
                  ];
            };


            var getConfiguracaoDeclaracaoCertidao = function (tipo) {
              return [{
                        status: true,
                        ordem: 0,
                        item: [{descricao: 'NOME/RAZÃO SOCIAL', status: true, hierarquia: 'PRIMARIO', identificador: 'NOME_RAZAO_SOCIAL'},
                               {descricao: 'CNPJ/CPF', status: true, hierarquia: 'PRIMARIO', identificador: 'CNPJ_CPF'},
                               {descricao: 'Nº DO ATENDIMENTO', status: true, hierarquia: 'PRIMARIO', identificador: 'NUMERO_ATENDIMENTO'}]
                      },{
                        status: true,
                        ordem: 1,
                        item: [{descricao: 'IMÓVEL', status: true, hierarquia: 'PRIMARIO', identificador: 'IMOVEL'},
                               {descricao:'RIP', status: true, hierarquia: 'SECUNDARIO', identificador: 'RIP'},
                               {descricao:'ENDEREÇO', status: true, hierarquia: 'SECUNDARIO', identificador: 'ENDERECO'},
                               {descricao:'ÁREA TOTAL DO TERRENO', status: true, hierarquia: 'SECUNDARIO', identificador: 'AREA_TOTAL_TERRENO'},
                               {descricao:'ÁREA TOTAL DA UNIÃO', status: true, hierarquia: 'SECUNDARIO', identificador: 'AREA_TOTAL_UNIAO'},
                               {descricao:'FRAÇÃO IDEAL', status: true, hierarquia: 'SECUNDARIO', identificador: 'FRACAO_IDEAL'},
                               {descricao:'REGIME', status: true, hierarquia: 'SECUNDARIO', identificador: 'REGIME'},
                               {descricao:'NATUREZA DA TRANSIÇÃO', status: true, hierarquia: 'SECUNDARIO', identificador: 'NATUREZA_TRANSICAO'},
                               {descricao:'RESPONSÁVEL ATUAL', status: true, hierarquia: 'SECUNDARIO', identificador: 'RESPONSAVEL_ATUAL'}]
                    },{
                        status: true,
                        ordem: 2,
                        item: [{descricao: 'CONTEÚDO', status: false, hierarquia: 'PRIMARIO', identificador: 'TITULO_CONTEUDO'},
                               {descricao: undefined, status: false, hierarquia: 'SECUNDARIO', identificador: 'VALOR_CONTEUDO'}]
                    },{
                        status: true,
                        ordem: 3,
                        item: [{descricao: undefined, status: true, hierarquia: 'PRIMARIO', identificador: 'COMPETENCIA_ASSINATURA'}]
                    },{
                        status: true,
                        ordem: 4,
                        item: [
                              {descricao: 'Data de Emissão: ', status: true, hierarquia: 'SECUNDARIO', identificador: 'DATA_EMISSAO'},
                              {descricao: 'Hora da Emissão: ', status: true, hierarquia: 'SECUNDARIO', identificador: 'HORA_EMISSAO'},
                              {descricao: 'Validade: ', status: true, hierarquia: 'SECUNDARIO', label: undefined, identificador: 'VALIDADE'},
                              {descricao: 'Código de Controle da Certidão/Declaração: ', status: true, hierarquia: 'SECUNDARIO', identificador: 'CODIGO_CONTROLE'},
                              {descricao: 'Esta certidão/declaração é fornecida gratuitamente, e sua autenticidade poderá ser confirmada no endereço: ', status: true, hierarquia: 'SECUNDARIO', identificador: 'AUTENTICIDADE_CONFIRMACAO_ENDEDERECO'}]
                    }
                ];
            };

            var getConfiguracaoPreview = function () {
              return [{label: 'titulo', valorLabel: 'MINISTÉRIO DO PLANEJAMENTO, DESENVOLVIMENTO E GESTÃO SECRETARIA DO PATRIMÔNIO DA UNIÃO'},
                      {label: 'textoUniao', valorLabel: 'União, por intermédio da Secretaria do Patrimônio da União, representado neste ato por:'},
                      {label: 'dataGerada', valorLabel: comumIntegradorService.diaExtenso()}];
            }

            var atualizarDicionario = function(item, descricao, label){
                angular.extend(item,{
                  descricao: angular.copy(descricao),
                  status: true,
                  label: label
                });

                console.log(label)
            };

            var atualizarClausulas = function(clausulas, config){
                angular.forEach(clausulas, function (clausulas, $index) {
                    config.item.push({
                    descricao: 'CLAUSULAS',
                    label: angular.copy(clausulas.descricao),
                    salvar: false,
                    hierarquia: 'SECUNDARIO',
                    identificador: getNumeroPorExtenso($index)
                  });
                });
            };

            var atualizarDicionarioDeclaracao = function (atosContratos, configuracaoComponentes) {
                angular.forEach(configuracaoComponentes, function (config) {
                  angular.forEach(config.item, function (item) {

                    if(item.identificador == 'VALOR_CONTEUDO'){
                      atualizarDicionario(item, atosContratos.conteudos.descricao);
                    }else if(item.identificador == 'COMPETENCIA_ASSINATURA'){
                      atualizarDicionario(item, atosContratos.tipocompetenciaAssinatura.descricao);
                    }else if(item.identificador == 'VALIDADE'){
                      atualizarDicionario(item, atosContratos.prazo);
                    }

                  });
                });
            };


            var atualizarPraias = function(configuracaoComponentes){
              var DADOS_IMOVEL = ['RIP','ENDERECO', 'AREA_TERRENO_CONTRATO',
                                      'NATUREZA_CONTRATO', 'MATRICULA_CARTORIAL', 'MEMORIAL_DESCRITIVO'];

                  angular.forEach(configuracaoComponentes, function(config){
                      var length = config.item.length;
                      var entrou = false;

                      for(var i=0; i<length-1; i++){
                        if(config.item[i] &&
                              DADOS_IMOVEL.indexOf(config.item[i].identificador) != -1){
                          config.item.splice(i,1);
                          entrou = true;
                          i--;
                        }
                      }

                      if(entrou){
                        entrou = false;
                        config.item.push({
                          descricao: 'Praias urbanas do Município',
                          status: true,
                          hierarquia: 'SECUNDARIO',
                          identificador: 'PRAIAS_URBANAS'
                        });
                      }
                  });
            };

            var atualizarDicionarioContrato = function (atosContratos, configuracaoComponentes) {

                angular.forEach(configuracaoComponentes, function(config){
                  var length = config.item.length;
                  for(var i=0; i<length; i++){
                    if(config.item[i].identificador == 'CONDICOES_CONTRATO'){
                        atualizarClausulas(atosContratos.clausulas, config);
                    }else if(config.item[i].identificador == 'DENOMINACAO_UNIAO'){
                      atualizarDicionario(config.item[i], atosContratos.denominacaoPartes.uniao,
                          getLabel('textoUniao', atosContratos.configuracaoAtosContratos));
                    }else if(config.item[i].identificador == 'DENOMINACAO_TERCEIRO'){
                      atualizarDicionario(config.item[i], atosContratos.denominacaoPartes.terceiro);
                    }
                  }
                });

                if(atosContratos.instrumentoFuncionalidade.id === GESTAO_PRAIA){
                      atualizarPraias(configuracaoComponentes);
                }
            };


            var atualizarModelo = function(atosContratos, configuracaoComponentes){

              angular.forEach(configuracaoComponentes, function(config){
                  angular.forEach(config.item, function(item){
                    if(atosContratos.denominacaoPartes){

                      if(atosContratos.denominacaoPartes.uniao && item.identificador == 'DENOMINACAO_UNIAO'){
                        atosContratos.denominacaoPartes.uniao = item.descricao;
                      }else if(atosContratos.denominacaoPartes && item.identificador == 'DENOMINACAO_TERCEIRO'){
                        atosContratos.denominacaoPartes.terceiro = item.descricao;
                      }

                    }else if(atosContratos.prazo && item.identificador == 'VALIDADE'){
                      atosContratos.prazo = parseInt(item.label);
                    }
                  });
              });
            };


            var getLabel = function (label, config) {
              var retorno = undefined;
              angular.forEach(config, function (item) {
                if(item.label == label){
                  retorno = item.valorLabel;
                }
              });
              return retorno;
            };

            var getDicionarioFinal = function (dictConfigurarComponente) {
              angular.forEach(dictConfigurarComponente, function(config){
                for(var i=0; i< config.item.length; i++){
                  if(config.item[i].salvar == false){
                     config.item.splice(i,1);
                     i--;
                  }
                }
              });

              return angular.copy(dictConfigurarComponente);
            };

            var getValorPorLabel = function (dictConfigurarComponente, indetificador) {
              var item = undefined;
              angular.forEach(dictConfigurarComponente, function(config){
                for(var i=0; i< config.item.length; i++){
                  if(config.item[i].identificador == indetificador){
                     item = config.item[i];
                  }
                }
              });
              return item.label;
            };

            var ordenaLista = function (atual, destino, lista) {
              var item = lista[atual];
              lista[atual] = lista[destino];
              lista[destino] = item;
            };

            var setarOrdem = function (configuracaoComponentes) {
              angular.forEach(configuracaoComponentes, function(config, $index){
                config.ordem = $index;
              });
            };

            var getNumeroPorExtenso = function(numero){
               var primeiraCasaDecimal = ['Primeira', 'Segunda', 'Terceira', 'Quarta', 'Quinta', 'Sexta', 'Sétima', 'Oitava', 'Nona', 'Décima'];
               var segundaCasaDecimal = ['Décima', 'Vigésima', 'Trigésima', 'Quadragésima', 'Quinquagésimo', 'Sexagésimo', 'Septuagésimo', 'Octogésimo', 'Nonagésimo'];

               var ordinal = '';

               if(numero < 10){
                  ordinal = primeiraCasaDecimal[numero];
               }else{
                  var strs =  numero.toString().split('');
                  ordinal = segundaCasaDecimal[parseInt(strs[0])-1];
                  ordinal += ' ' + primeiraCasaDecimal[strs[1]];
               }

               return $filter('translate')('clausula') + ' ' + ordinal;
             };



            return {
                buscarTipoModeloContrato: buscarTipoModeloContrato,
                buscarDespachoPorFuncionalidade: buscarDespachoPorFuncionalidade,
                buscarTipoCompetenciaAssinatura: buscarTipoCompetenciaAssinatura,
                getConfiguracaoInicialContratoTermo: getConfiguracaoInicialContratoTermo,
                getConfiguracaoDeclaracaoCertidao: getConfiguracaoDeclaracaoCertidao,
                getConfiguracaoPreview: getConfiguracaoPreview,
                selecionarTodos: selecionarTodos,
                buscarModulos: buscarModulos,
                buscarFuncionalidadePorModulo: buscarFuncionalidadePorModulo,
                ordenaLista: ordenaLista,
                buscarFundamentoLegalFuncionalidade: buscarFundamentoLegalFuncionalidade,
                getNumeroPorExtenso: getNumeroPorExtenso,
                salvar: salvar,
                atualizarDicionarioDeclaracao: atualizarDicionarioDeclaracao,
                atualizarDicionarioContrato: atualizarDicionarioContrato,
                buscarAlterarAtoContrato: buscarAlterarAtoContrato,
                buscarCodPrefixo: buscarCodPrefixo,
                buscarTodosAtosContratos: buscarTodosAtosContratos,
                filterBuscarTodosAtosContratos: filterBuscarTodosAtosContratos,
                excluirModeloAtosContratos: excluirModeloAtosContratos,
                getDicionarioFinal: getDicionarioFinal,
                setarOrdem: setarOrdem,
                getLabel: getLabel,
                atualizarModelo: atualizarModelo,
                getValorPorLabel: getValorPorLabel
            };
        }
})();

(function(){
    'use strict';
        angular
        .module('su-integrador')
        .controller('previewController',
                    ['$scope', 'escopoCompartilhadoService', '$filter', 'atosContratosService',
                    'localStorageService', 'comumIntegradorService', '$state', 'ComumFactory',
            function ($scope, escopoCompartilhadoService, $filter, atosContratosService,
                      localStorageService, comumIntegradorService, $state, ComumFactory) {

          var vm = $scope;

          vm.TIPO_CONTRATO_TERMO = 1;
          vm.TIPO_DECLARACAO_CERTIDAO = 2;

          vm.formmatarCodigoGerado = comumIntegradorService.formmatarCodigoGerado;
          vm.ordemCampos = atosContratosService.ordenaLista;
          vm.getLabel = atosContratosService.getLabel;
          vm.removerHTML = comumIntegradorService.removerHTML;


          var init = function () {

            vm.atoTermo = escopoCompartilhadoService.getObjeto('atosContratos');

            // vm.atoTermo = localStorageService.get('tijolo');

            if(!vm.atoTermo){
              $state.go('INTEGRADOR.ATOS_E_CONTRATOS');
            }

            buscarCodPrefixo();

            if(!vm.atoTermo.id){
              vm.dictConfigurarComponente = angular.copy(getDicionarioInicial());
              vm.atoTermo.configuracaoAtosContratos = angular.copy(
                                    atosContratosService.getConfiguracaoPreview());
            }else {
              vm.dictConfigurarComponente = angular.copy(vm.atoTermo.configuracaoComponentes);
            }

            if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_DECLARACAO_CERTIDAO){
              atosContratosService.atualizarDicionarioDeclaracao(vm.atoTermo, vm.dictConfigurarComponente);

            }else if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
              atosContratosService.atualizarDicionarioContrato(vm.atoTermo, vm.dictConfigurarComponente);
            }


            vm.tituloMinisterio = atosContratosService.getLabel('titulo',
                                           vm.atoTermo.configuracaoAtosContratos);
          };

          var buscarCodPrefixo = function () {
            if(angular.isDefined(vm.atoTermo.prefixo)){
              atosContratosService.buscarCodPrefixo(vm.atoTermo.prefixo).then(
                function (resposta) {
                  vm.codigoGerado = resposta.data.resposta[0];
              });
            }
          };

          var getDicionarioInicial = function(){
              if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
                  return angular
                      .copy(atosContratosService.getConfiguracaoInicialContratoTermo());

              }else if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_DECLARACAO_CERTIDAO){
                  return angular
                      .copy(atosContratosService.getConfiguracaoDeclaracaoCertidao());
              }
          };

          var mesangemSucesso = function () {
              ComumFactory.mostrarMensagemSucesso('Salvo com Sucesso.');
          };

          var atualizarModelo = function(){
            if(vm.atoTermo){
              vm.atoTermo.configuracaoComponentes = atosContratosService
                          .getDicionarioFinal(vm.dictConfigurarComponente);

              atosContratosService.setarOrdem(vm.atoTermo.configuracaoComponentes);
              atosContratosService.atualizarModelo(vm.atoTermo, vm.atoTermo.configuracaoComponentes);

              angular.forEach(vm.atoTermo.configuracaoAtosContratos, function(config){
                if(config.label == 'titulo'){
                  config.valorLabel = vm.tituloMinisterio;
                }else if(config.label == 'textoUniao'){
                  config.valorLabel = atosContratosService
                        .getValorPorLabel(vm.atoTermo.configuracaoComponentes, 'DENOMINACAO_UNIAO');
                }
              });

              escopoCompartilhadoService.setObjetos('atosContratos', vm.atoTermo);
            }
          };

          vm.voltar = function () {
            atualizarModelo();
            $state.go('INTEGRADOR.ATOS_E_CONTRATOS');
          };

          vm.salvar = function(){
            atualizarModelo();
            atosContratosService.salvar(vm.atoTermo).then(function(res){
                  mesangemSucesso();
                  escopoCompartilhadoService.limparEscopo();
                  $state.go('INTEGRADOR.CONSULTAR_ATOS_E_CONTRATOS');
            }, function(erro){
                  ComumFactory.mostrarMensagemError(erro.data.mensagem[0]);
            });
          };

          init();

        }])
})();

(function(){
  'use strict';
  angular
    .module('su-integrador')
    .controller('consultarAtosContratosTermoController',
      ['$scope', '$mdDialog', 'atosContratosService', '$q', 'FormularioFactory',
                '$state', '$filter', 'escopoCompartilhadoService',
        function ($scope, $mdDialog, atosContratosService, $q, FormularioFactory,
                $state, $filter, escopoCompartilhadoService) {

          var vm = $scope;

          vm.LIMPAR = 'LIMPAR';
          vm.PESQUISAR = 'PESQUISAR';
          vm.REMOVER = 'REMOVER';
          vm.NOVO = "NOVO";

          vm.filtro = {};
          vm.listaAtosContratos = [];

          vm.validarFormulario = FormularioFactory.validarFormularioComMensagem;

          var init = function(){
            vm.registroInicial = 1;
            vm.tamanhoLimite = 5;
            vm.totalItems = 0;

            escopoCompartilhadoService.limparEscopo();

            $q.all([atosContratosService.buscarTipoModeloContrato(),
              atosContratosService.buscarTipoCompetenciaAssinatura(),
              atosContratosService.buscarModulos()])
              .then(function(res){
                if(res[0]){
                  vm.tipoModelo = res[0].data.resposta;
                }
                if(res[1]){
                  vm.tipoCompetenciaContrato = res[1].data.resposta;
                }
                if(res[2]){
                  vm.modulos = res[2].data.resposta;
                }
              });
            buscarDadosModelos();
          };

          vm.buscarFuncionalidadePorModulo = function(){
            if(vm.filtro.modulo){
              atosContratosService.buscarFuncionalidadePorModulo(vm.filtro.modulo.id)
                .then(function(resultado){
                  vm.funcionalidades = resultado.data.resposta;
                });
            }
          };

          vm.limparCamposPesquisa = function() {
            vm.filtro = {};
          };

          vm.modalConfirmarExclusaoAtos = function ($event, idContrato) {
            var confirm = $mdDialog.confirm({focusOnOpen: false})
              .title($filter('translate')('titulo-excluir-modelo'))
              .textContent($filter('translate')('msg-confirmar-exclusao'))
              .targetEvent($event)
              .ok($filter('translate')('rb-sim'))
              .cancel($filter('translate')('rb-nao'));

            $mdDialog.show(confirm).then(function () {
                vm.abrirModalExcluirModelo($event, idContrato);
            }, function () {
                $mdDialog.cancel();
            });
          };

          vm.abrirModalExcluirModelo = function (ev, idContrato) {

            $mdDialog.show({
              controller: ["$scope", function ($scope) {
                var init = function(){
                  $scope.justificativaExclusao = '';
                };

                $scope.confirmar = function(){
                  var atosContratos = {
                    id: idContrato,
                    justificativa: $scope.justificativaExclusao
                  };

                  atosContratosService.excluirModeloAtosContratos(atosContratos).then(function(){
                    vm.buscarDadosModelosPaginacao();
                    $scope.fechar();
                  });
                };

                $scope.fechar = function () {
                  $mdDialog.cancel();
                };

                init();

                $scope.closeDialog = function () {
                  $mdDialog.cancel();
                };

              }],
              templateUrl: 'scripts-integrador/pages/atos-contratos/view/partials/modalExclusaoModelo.html',
              parent: angular.element(document.body),
              targetEvent: ev,
              clickOutsideToClose: false,
              locals: {
              }
            });
          };


          var buscarDadosModelos = function() {
            atosContratosService.buscarTodosAtosContratos((vm.registroInicial - 1), vm.tamanhoLimite)
                  .then(function(resp) {
              vm.listaAtosContratos = resp.data.resposta[0].content;
              vm.totalItems = resp.data.resposta[0].totalElements;
            });
          };

          var buscarPorFiltroDadosModelos = function() {
            var filtroAtoTermo = {
              descricao: vm.filtro.descricao ? vm.filtro.descricao : null,
              modulo: vm.filtro.modulo ? vm.filtro.modulo.constante : null,
              tipoModeloContrato: vm.filtro.tipoModeloContrato ? vm.filtro.tipoModeloContrato.constante : null,
              instrumentoFuncionalidade: vm.filtro.instrumentoFuncionalidade ?
                      vm.filtro.instrumentoFuncionalidade.tipoInstrumentoFuncionalidadeEnum : null
            };

            atosContratosService.filterBuscarTodosAtosContratos(filtroAtoTermo, (vm.registroInicial - 1), vm.tamanhoLimite)
              .then(function(resp) {
                vm.listaAtosContratos = resp.data.resposta[0].content;
                vm.totalItems = resp.data.resposta[0].totalElements;
              });
          };

          vm.buscarDadosModelosPaginacao = function() {
            if ((vm.filtro.descricao && vm.filtro.descricao != "")
              || (vm.filtro.modulo && vm.filtro.modulo != "")
              || (vm.filtro.tipoModeloContrato && vm.filtro.tipoModeloContrato != "")
              || (vm.filtro.instrumentoFuncionalidade && vm.filtro.instrumentoFuncionalidade != "")) {
              buscarPorFiltroDadosModelos();
            } else {
              buscarDadosModelos();
            }
          };

          vm.redirecionarNovoAtoContrato = function() {
            escopoCompartilhadoService.limparEscopo();
            $state.go('INTEGRADOR.ATOS_E_CONTRATOS');
          };

          init();

        }]);
})();

(function(){
    'use strict';
        angular
        .module('su-integrador')
        .controller('cadastroContratoTermoController',
                    ['$scope', '$mdDialog', 'atosContratosService', '$q', 'FormularioFactory', '$filter',
                      'ComumFactory', 'escopoCompartilhadoService', '$state', '$stateParams', 'localStorageService',
                      'instrumentoFuncionalidadeService', 'comumIntegradorService',
            function ($scope, $mdDialog, atosContratosService, $q, FormularioFactory, $filter, ComumFactory,
                      escopoCompartilhadoService, $state, $stateParams, localStorageService,
                      instrumentoFuncionalidadeService, comumIntegradorService) {


          var vm = $scope;

          vm.PRE_VISUALIZAR = 'PRE_VISUALIZAR';
          vm.EDITAR = 'EDITAR';
          vm.acaoSubmit = '';

          vm.TIPO_CONTRATO_TERMO = 1;
          vm.TIPO_DECLARACAO_CERTIDAO = 2;

          vm.isEditar = false;
          vm.removerHTML = comumIntegradorService.removerHTML;

          var init = function(){

              vm.atoTermo = escopoCompartilhadoService.getObjeto('atosContratos');

              if(vm.atoTermo){
                preencherAtosContratos();
              }else {
                escopoCompartilhadoService.limparEscopo();

                if (!vm.atoTermo && $stateParams.idContrato) {
                  atosContratosService.buscarAlterarAtoContrato($stateParams.idContrato).then(function(resp) {
                    vm.atoTermo = resp.data.resposta[0];
                    preencherAtosContratos();
                    vm.atoTermo.despacho = angular.copy(vm.atoTermo.despacho);
                    if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
                      vm.atoTermo.denominacaoPartes.uniao = angular.copy(vm.atoTermo.denominacaoPartes.uniao);
                      vm.atoTermo.denominacaoPartes.terceiro = angular.copy(vm.atoTermo.denominacaoPartes.terceiro);
                    }
                  });
                } else if(!vm.atoTermo){
                    vm.atoTermo = {
                      cadastramento: new Date()
                    };
                  }
              }

              vm.acaoSubmit = undefined;

              $q.all([atosContratosService.buscarTipoModeloContrato(),
                      atosContratosService.buscarTipoCompetenciaAssinatura(),
                      atosContratosService.buscarModulos()])
                      .then(function(res){

                      if(res[0]){
                          vm.tipoModelo = res[0].data.resposta;
                      }

                      if(res[1]){
                          vm.tipoCompetenciaContrato = res[1].data.resposta;
                      }

                      if(res[2]){
                          vm.modulos = res[2].data.resposta;
                      }

              });

              vm.configPreambulo = {
                  titulo : $filter('translate')('label-preambulo'),
                  descricao: $filter('translate')('descricao-modelo'),
                  labelEditar: $filter('translate')('editar-campo', {
                      'campo': $filter('translate')('label-preambulo')
                  }),
                  labelExcluir: $filter('translate')('excluir-campo', {
                      'campo': $filter('translate')('label-preambulo')
                  })
              };

              vm.configConteudo  = {
                  titulo : $filter('translate')('label-conteudo'),
                  descricao: $filter('translate')('descricao-modelo'),
                  labelEditar: $filter('translate')('editar-campo', {
                      'campo': $filter('translate')('label-conteudo')
                  }),
                  labelExcluir: $filter('translate')('excluir-campo', {
                      'campo': $filter('translate')('label-conteudo')
                  })
              };

          };

          var preencherAtosContratos = function () {
            vm.atoTermo.cadastramento = new Date(vm.atoTermo.cadastramento);
            vm.buscarFuncionalidadePorModulo();
            /* vm.selecionarTipo();*/
            vm.buscarDespachoPorFuncionalidade();
          };

          vm.buscarFundamentoLegalFuncionalidade = function () {
            if(vm.atoTermo.instrumentoFuncionalidade){
              atosContratosService.buscarFundamentoLegalFuncionalidade(vm.atoTermo.instrumentoFuncionalidade.id)
                  .then(function (resultado) {
                        vm.fundamentos = resultado.data.resposta;
                  });
            }
          };

          vm.buscarFuncionalidadePorModulo = function(){
              if(vm.atoTermo.modulo){
                atosContratosService.buscarFuncionalidadePorModulo(vm.atoTermo.modulo.id)
                            .then(function(resultado){
                        vm.funcionalidades = resultado.data.resposta;

                });

              }
          };

          vm.limpaModulosEInstrumentos = function(){
            vm.despacho = undefined;
            vm.atoTermo.instrumentoFuncionalidade = undefined;
          };

          vm.buscarDespachoPorFuncionalidade = function () {
            if(vm.atoTermo.instrumentoFuncionalidade){
              atosContratosService.buscarDespachoPorFuncionalidade(vm.atoTermo.instrumentoFuncionalidade.id)
                            .then(function (resultado) {
                vm.despacho = resultado.data.resposta;
              });
            }
          };
          vm.selecionarTipo = function(){
              vm.atoTermo.clausulas = undefined;
              vm.atoTermo.conteudo = undefined;
              vm.atoTermo.preambulo = undefined;
              vm.atoTermo.denominacaoPartes = undefined;

              getDicionarioInicial();
          };

          var getDicionarioInicial = function(){
              if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
                  vm.atoTermo.configuracaoComponentes = angular
                      .copy(atosContratosService.getConfiguracaoInicialContratoTermo());
                  //adicionarInformacoesDenomicacaoDasPartes(config);

              }else if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_DECLARACAO_CERTIDAO){
                  vm.atoTermo.configuracaoComponentes = angular
                      .copy(atosContratosService.getConfiguracaoDeclaracaoCertidao());
              }
          };

          vm.validarFormulario = function (form) {
            var valido = FormularioFactory.validarFormularioComMensagem(form);
             if(valido && vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
               if(!vm.atoTermo.preambulo || !vm.atoTermo.clausulas){
                 ComumFactory.mostrarMensagemError($filter('translate')('erro-campos-obrigatorio'));
                 valido = false;
               }
             }else if(valido && vm.atoTermo.tipoModeloContrato.id == vm.TIPO_DECLARACAO_CERTIDAO){
               if(!vm.atoTermo.preambulo || !vm.atoTermo.conteudos){
                 ComumFactory.mostrarMensagemError($filter('translate')('erro-campos-obrigatorio'));
                 valido = false;
               }
             }
             return valido;
          };

          vm.enviarFormulario = function(){
            if(vm.acaoSubmit == vm.PRE_VISUALIZAR){
                escopoCompartilhadoService.setObjetos('atosContratos', vm.atoTermo);

                //localStorageService.set('tijolo', vm.atoTermo)
                if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_CONTRATO_TERMO){
                  $state.go('INTEGRADOR.MODELO_CONTRATO');

                }else if(vm.atoTermo.tipoModeloContrato.id == vm.TIPO_DECLARACAO_CERTIDAO){
                      $state.go('INTEGRADOR.MODELO_CERTIDAO');
                }
            }
          };

          vm.fechar = function () {
            $state.go('INTEGRADOR.CONSULTAR_ATOS_E_CONTRATOS');
          };

          init();

    }]);
})();

(function () {

  'use strict';

  directive.$inject = ["$document"];
  angular
       .module('su-integrador')
      .directive('iframeSetDimensionsOnload', directive);

    function directive($document) {

    return {
      link: function(scope, element, attrs, modelCtrl) {

            var conteudo = angular.element($document[0].querySelector('#conteudo'));
            var height = conteudo.prop('offsetHeight');
          
            var iFrameHeight = (height + 1000) + 'px';
            var iFrameWidth = '100%';

            element.css('width', iFrameWidth);
            element.css('height', iFrameHeight);
            element.css('border-width',  '0px');
      }
    };
  }
  
 })();

(function () {

  'use strict';

  directive.$inject = ["$interval", "$rootScope", "$state"];
  angular.module('su-integrador')
      .directive('capitalize', directive);

    function directive($interval, $rootScope, $state) {

    return {
      require: 'ngModel',
      link: function(scope, element, attrs, modelCtrl) {

        var capitalize = function(inputValue) {
          if (inputValue == undefined) inputValue = '';
          var capitalized = inputValue.toUpperCase();
          if (capitalized !== inputValue) {
            modelCtrl.$setViewValue(capitalized);
            modelCtrl.$render();
          }
          return capitalized;
        };

        modelCtrl.$parsers.push(capitalize);
        capitalize(scope[attrs.ngModel]);
      }
    };
  }
  
 })();

(function () {
  'use strict';

          directive.$inject = ["$mdDialog", "$filter"];
    angular
        .module('su-integrador')
        .directive('adicionarEditorHtml', directive);

  function directive($mdDialog, $filter) {
            return {
                restrict: 'EA',
                scope: {
                    config: '=',
                    model:'=ngModel'
                },
                templateUrl: 'scripts-integrador/app/directives/secoes/templates/adicionarEditorHtml.html',
                link: function ($scope) {

                    var vm = $scope;

                    vm.abrir = function (ev, itemEditar) {
                        $mdDialog.show({
                            controller: ["$scope", function ($scope) {

                                var init = function(){
                                  $scope.titulo = vm.config.titulo;
                                  $scope.descricao = vm.config.descricao;

                                  $scope.editar = (itemEditar !== undefined);
                                  $scope.item = ($scope.editar) ?
                                            angular.copy(itemEditar) : undefined;

                                };

                                var validar = function(){
                                    return ($scope.item);
                                };

                                $scope.confirmar = function(){
                                    if(validar()){
                                        vm.model = $scope.item;
                                        $scope.fechar();
                                    }
                                };

                                $scope.fechar = function () {
                                    $mdDialog.cancel();
                                };

                                init();

                            }],
                            templateUrl: 'scripts-integrador/app/directives/secoes/templates/partials/modalAdicionarEditorHtml.html',
                            parent: angular.element(document.body),
                            targetEvent: ev,
                            clickOutsideToClose: false,
                            locals: {
                            }
                        });
                };


                vm.remover = function(ev) {
                    var confirm = $mdDialog.confirm({focusOnOpen:false})
                        .title($filter('translate')('excluir'))
                        .textContent($filter('translate')('msg-excluir', {
                             campo: vm.config.titulo
                         }))
                        .targetEvent(ev)
                        .ok($filter('translate')('rb-sim'))
                        .cancel($filter('translate')('rb-nao'));
                    $mdDialog.show(confirm).then(function() {

                        vm.model = undefined;

                   }, function() {
                        $mdDialog.cancel();
                    });
                };

                vm.editar = function(ev){
                    vm.abrir(ev, vm.model);
                };

                var init = function(){
                    vm.labelEditar = vm.config.labelEditar;
                    vm.labelExcluir = vm.config.labelExcluir;
                };

                init();

                }
              };
          }
}());

(function () {

          directive.$inject = ["atosContratosService", "$mdDialog", "$filter"];
    angular
        .module('su-integrador')
        .directive('adicionarClausulas', directive);

         function directive(atosContratosService, $mdDialog, $filter) {
            return {
                restrict: 'EA',
                scope: {
                   clausulas: '=ngModel'
                },
                templateUrl: 'scripts-integrador/app/directives/secoes/templates/adicionarClausulas.html',
                link: function ($scope) {

                    var vm = $scope;


                    vm.abrir = function (ev) {
                        $mdDialog.show({
                            controller: ["$scope", function ($scope) {

                                var init = function(){
                                    $scope.ordemCampos = atosContratosService.ordenaLista;
                                    $scope.getNumeroPorExtenso = atosContratosService.getNumeroPorExtenso;
                                    $scope.listClausulas = (vm.clausulas !== undefined) ?
                                                     angular.copy(vm.clausulas) : [];
                                    $scope.adicionarClausula();
                                };

                                $scope.adicionarClausula = function(){
                                    $scope.listClausulas.push({descricao: undefined});
                                };

                                $scope.confirmar = function(){
                                  if($scope.formClausula.$invalid){
                                    return;
                                  }
                                    vm.clausulas = [];
                                    angular.forEach($scope.listClausulas, function(item, $index){
                                        if(item.descricao){
                                            item.ordem = $index;
                                            vm.clausulas.push(item);
                                        }
                                    });
                                    $scope.fechar();
                                };

                                $scope.fechar = function () {
                                    $mdDialog.cancel();
                                };

                                $scope.remover = function(index){
                                    $scope.listClausulas.splice(index, 1);
                                };

                                init();

                            }],
                            templateUrl: 'scripts-integrador/app/directives/secoes/templates/partials/modalClausulas.html',
                            parent: angular.element(document.body),
                            targetEvent: ev,
                            clickOutsideToClose: false,
                            locals: {
                            }
                        });
                    };


                    vm.remover = function(ev, index) {
                        var confirm = $mdDialog.confirm({focusOnOpen:false})
                            .title($filter('translate')('excluir'))
                            .textContent($filter('translate')('msg-excluir',  {
                                campo: $filter('translate')('label-clausulas')
                            }))
                            .targetEvent(ev)
                            .ok($filter('translate')('rb-sim'))
                            .cancel($filter('translate')('rb-nao'));
                        $mdDialog.show(confirm).then(function() {
                              vm.clausulas.splice(index, 1);
                        }, function() {
                            $mdDialog.cancel();
                        });
                    };


                    var init = function(){
                        vm.labelExcluir = $filter('translate')('excluir-campo', {
                            campo: $filter('translate')('label-clausulas')
                        });

                        vm.labelEditar = $filter('translate')('editar-campo', {
                            campo: $filter('translate')('label-clausulas')
                        });
                    };

                    init();

                }
              };
          }
}());

(function () {

  directive.$inject = ["fundamentoLegalService", "$mdDialog"];
  angular
    .module('su-integrador')
    .directive('incluirUtilizacao', directive);

  function directive(fundamentoLegalService, $mdDialog) {
    return {
      restrict: 'EA',
      scope: {
        utilizacoes: '=',
        bloquear: '=',
        isDetalhar: '='
      },
      templateUrl: 'scripts-integrador/app/directives/fundamento-legal/template/incluirUtilizacao.html',
      link: function ($scope) {

        var vm = $scope;

        vm.utilizacao = {tipoUsoPermitido: [], especificacoes: []};

        vm.listaTipoUsoPermitido = [];
        vm.listaEspecificacao = [];

        var init = function(){
          vm.utilizacoes = vm.utilizacoes || [];
          buscarTipoUsoPermitido();
          vm.buscarEspecificacoes();
        };

        vm.buscarEspecificacoes = function () {
          fundamentoLegalService.buscarTodasEspecificacoes().then(function (resp) {
            vm.especificacoes = resp.data.resposta;
          })
        };

        vm.validarExibicaoEspecificacao = function (item) {
          var qntEspecificacoesTabela = 0;
          angular.forEach(vm.utilizacoes, function (utilizacao) {
            angular.forEach(utilizacao.especificacoes, function (especificacao) {
              if (especificacao.id === item.id) {
                qntEspecificacoesTabela ++;
              }
            })
          });
          return qntEspecificacoesTabela === 0;
        };


        vm.validarExibicaoTipoPermitido = function (item) {
            var qntEspecificacoesPorTipo = 0;
            var qntEspecificacoesTabela = 0;
          angular.forEach(vm.especificacoes, function (especificacao) {
             if (especificacao.tipoUtilizacao.id === item.id) {
               qntEspecificacoesPorTipo ++;
             }
          });

          angular.forEach(vm.utilizacoes, function (utilizacao) {
            angular.forEach(utilizacao.especificacoes, function (especificacao) {
              if (especificacao.tipoUtilizacao.id === item.id) {
                qntEspecificacoesTabela ++;
              }
            })
          });
          return qntEspecificacoesPorTipo > qntEspecificacoesTabela;
        };

        vm.selecionarTodosTipo = function(todosTipos) {
          if(todosTipos){
            fundamentoLegalService.buscarTodasEspecificacoes().then(function (resp) {
              vm.utilizacao.tipoUsoPermitido = vm.listaTipoUsoPermitido;
              vm.listaEspecificacao = resp.data.resposta;
            });
          } else{
            vm.utilizacao.tipoUsoPermitido = [];
            vm.listaEspecificacao = [];
          }

        };

        vm.selecionarTodosEspecificacao = function (tipoEspecificacao) {
          if(tipoEspecificacao){
            vm.utilizacao.especificacoes = vm.listaEspecificacao;
          } else {
            vm.utilizacao.especificacoes = [];
          }
        };

        vm.adicionarListaUtilizacoes = function() {
          var existe = false;
          if (vm.utilizacao.tipoUsoPermitido && vm.utilizacao.especificacoes) {
            angular.forEach(vm.utilizacao.tipoUsoPermitido, function (tipoUsoPermitido) {
              angular.forEach(vm.utilizacoes, function (utilizacao) {
                 if (tipoUsoPermitido.id === utilizacao.tipoUsoPermitido.id) {
                   existe = true;
                   var especificacoesSelecionadas = [];
                    angular.forEach(vm.utilizacao.especificacoes, function (especificacao) {
                         if (especificacao.tipoUtilizacao.id === tipoUsoPermitido.id) {
                           especificacoesSelecionadas.push(especificacao);
                         }
                       });
                   var especificacoesTabela = utilizacao.especificacoes;
                   utilizacao.especificacoes = especificacoesTabela.concat(especificacoesSelecionadas);
                 }
              });
            });

            if (!existe) {
              angular.forEach(vm.utilizacao.tipoUsoPermitido, function (tipoUsoPermitido) {
                var especificacoes = [];
                angular.forEach(vm.utilizacao.especificacoes, function (especificacao) {
                  if (especificacao.tipoUtilizacao.id === tipoUsoPermitido.id) {
                    especificacoes.push(especificacao);
                  }
                });
                if (especificacoes.length > 0) {
                  vm.utilizacoes.push({tipoUsoPermitido: tipoUsoPermitido, especificacoes: especificacoes});
                }
              });
            }
          }
          vm.utilizacao.tipoUsoPermitido = undefined;
          vm.utilizacao.especificacoes = undefined;
        };

        var buscarTipoUsoPermitido = function () {
          fundamentoLegalService.pesquisarTodosTipoUsoPermitido().then(function (resp) {
            vm.listaTipoUsoPermitido = resp.data.resposta;
          })
        };

        vm.buscarEspecificacao = function () {
          vm.listaEspecificacao = [];
          vm.utilizacao.especificacoes = [];
          if(vm.utilizacao.tipoUsoPermitido.length < vm.listaTipoUsoPermitido.length){
            angular.forEach(vm.utilizacao.tipoUsoPermitido, function (tipoUso) {
              fundamentoLegalService.pesquisarEspecificacao(tipoUso.id).then(function (resp) {
                vm.listaEspecificacao = vm.listaEspecificacao.concat(resp.data.resposta);
              })
            })
          }
        };

        vm.removerUsoEspecificacao = function($event, index, itemUsoEspecificacao) {
          var confirm = $mdDialog.confirm({focusOnOpen: false})
            .title('Excluir')
            .textContent('Deseja remover este registro?')
            .targetEvent($event)
            .ok('Sim')
            .cancel('Não');

          $mdDialog.show(confirm).then(function () {
            itemUsoEspecificacao.especificacoes.splice(index,1);
            if (itemUsoEspecificacao.especificacoes.length === 0) {
              vm.utilizacoes.splice(indexOfTipoUsoPermitido(itemUsoEspecificacao),1);
            }
          }, function () {
            $mdDialog.cancel();
          });
        };

        function indexOfTipoUsoPermitido(itemUsoEspecificacao) {
          for (var i = 0; i < vm.utilizacoes.length; i ++) {
            if (itemUsoEspecificacao.tipoUsoPermitido.id === vm.utilizacoes[i].tipoUsoPermitido.id) {
              return i;
            }
          }

        }

        $scope.$watch('listaEspecificacao', function() {
          for(var index in vm.listaEspecificacao){
            for(var index2 in vm.utilizacoes){
              for(var index3 in vm.utilizacoes[index2].especificacoes){
                if(vm.listaEspecificacao[index].descricao === vm.utilizacoes[index2].especificacoes[index3].descricao){
                  vm.listaEspecificacao.splice(index, 1);
                }
              }
            }
          }
        });

          init();

      }
    };
  }
}());

angular.module('su-integrador')
.directive('carousel', function() {
  return {
    restrict:'EA',
    scope:{
      imagens:'='
    },
    replace:false,
    controller:["$scope", function($scope){
      $scope.selectedIndex=0;

      $scope.avanca=function(){
        if($scope.selectedIndex <=$scope.imagens.length){
          $scope.selectedIndex++;
        }
      };

      $scope.retorna=function(){
        if($scope.selectedIndex >0){
          $scope.selectedIndex--;
        }
      };

    }],
    templateUrl: 'scripts-integrador/app/directives/carousel/carousel.html'
  };
});

/**
 * Created by Basis Tecnologia on 27/10/2016.
 */
(function(){

  'use strict';

  menuService.$inject = ["$rootScope", "localStorageService"];
  angular
    .module('su-integrador')
    .service('menuService', menuService);

  function menuService($rootScope, localStorageService) {

    var ADMINSTRACAO = 'INTEGRADOR.ADMINISTRACAO';   

    function getNomePaginaAtual (rota) {
      var menus = localStorageService.get('menu');
      var menusDestinacao = getMenu(menus);
      if (menusDestinacao) {
        getNomePaginaItemMenu(menusDestinacao, rota);
      }

    }

    function getNomePaginaItemMenu (menu, rota) {
      if (menu.codigoIdentificador === rota) {
        $rootScope.paginaAtual = menu.descricaoResumida;
        localStorageService.set('menuAnterior', $rootScope.paginaAtual);
      } else {
        $rootScope.paginaAtual =  localStorageService.get('menuAnterior');
        lerItemMenu(menu.nos, rota);
      }
    }

    function lerItemMenu(menus, rota) {
      if (menus) {
        for (var i = 0; i < menus.length; i++) {
          getNomePaginaItemMenu(menus[i], rota);
        }
      }

    }

    function getMenu(menus) {
      var indice = undefined;
      if (menus) {
        for (var i = 0; i < menus.length; i++) {
          if (ADMINSTRACAO == menus[i].codigoIdentificador) {
            indice = i;
            break;
          }
        }
        return menus[indice];
      }
      return undefined;
    }

    return {
      getNomePaginaAtual: getNomePaginaAtual
    }
  }

})();
(function () {

  'use strict';

  angular
    .module('su-integrador')
    .factory('integradorEscopoCompartilhadoService', service);

  function service () {
    var _objetos = {};

    var _urlsPorAmbiente = {};

    function setObjetos (nomeObjeto, objeto) {
      _objetos[nomeObjeto] = objeto;
    }

    function getObjeto(nomeObjeto) {
      return _objetos[nomeObjeto];
    }

    function setUrlsPorAmbiente(urls) {
      return _urlsPorAmbiente = urls;
    }

    function getUrlsPorAmbiente() {
      return _urlsPorAmbiente;
    }

    function limparEscopo(){
      _objetos = {};
    }

    return {
      setObjetos: setObjetos,
      getObjeto: getObjeto,
      setUrlsPorAmbiente: setUrlsPorAmbiente,
      getUrlsPorAmbiente: getUrlsPorAmbiente,
      limparEscopo: limparEscopo
    };
  }

})();

(function(){

  'use strict';

  angular
    .module('su-integrador')
    .service('formularioIntegradorService', ["ComumFactory", "$filter", function (ComumFactory,$filter) {

      var instance = function () {


        var verificarValidade = function (form) {
          return !form.$valid;
        };

        var verificarObrigatoriedade = function (form) {

          var error = form.$error;
          var invalido = false;

          angular.forEach(error.required, function (field) {
            if (field.$invalid) {

              invalido = true;

            }
          });

          return invalido;
        };

        this.validarFormulario = function (form) {

          form.$setSubmitted();
          var invalido = verificarObrigatoriedade(form);

          if (invalido) {
            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
          }

          return !invalido;
        };


      };

      return {
        getInstance: function () {
          return new instance();
        }
      }

    }])
})();

/**
 * Created by Basis Tecnologia on 27/10/2016.
 */
(function(){

  'use strict';

  service.$inject = ["$http", "$translate", "$rootScope", "localStorageService"];
  angular
    .module('su-integrador')
    .service('comumIntegradorService', service);

  function service($http,$translate,$rootScope, localStorageService) {


    var meses = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho',
                  'Agosto','Setembro','Outubro','Novembro','Dezembro']

    var formmatarCodigoGerado = function (codigoGerado) {
      if(angular.isDefined(codigoGerado)){

        var prefixo = codigoGerado.prefixo;
        var numero = codigoGerado.sequencial;
        var ano = codigoGerado.ano;

        return prefixo + lPad(numero, 4) + '/' + ano;
      }
    };


    var removerHTML = function(texto) {
         return texto.replace(/<.*?>/g, '');
    };

    var lPad = function (number, digits) {
      return Array(Math.max(digits - String(number).length + 1, 0)).join(0) + number;
    };

    var diaExtenso = function () {

      var data = new Date();
      var dia = data.getDate();
      var mes = data.getMonth();
      var ano = data.getYear();

      if (navigator.appName == "Netscape") {
        ano = ano + 1900;
      }

      var diaext = dia + ' de ' + meses[mes] + ' de ' + ano;
      return diaext;

    };

    return {
      lPad: lPad,
      formmatarCodigoGerado: formmatarCodigoGerado,
      diaExtenso: diaExtenso,
      removerHTML: removerHTML,

    };
  }

})();

(function () {
  'use strict';

  service.$inject = ["Upload", "$http"];
  function service(Upload, $http) {

    var URL = '/integrador/api/arquivo/';

    var apagar = function (id) {
      return $http.delete(URL + id);
    };

    var uploadArquivo = function($file){
      if ($file.id){
        return $http.post(URL + 'atualizar', $file);
      } else {
          return Upload.upload({
            url: URL + 'upload',
            data: {
              file: $file
            }
          });
      }

    };

    var gerarPreviewImagens = function($file, callback){
      if(!$file){
        return;
      }

      var reader  = new FileReader();

      reader.addEventListener("load", function () {
        (callback || angular.noop)(reader.result);
      }, false);

      reader.readAsDataURL($file);
    };

    var gerarSRCImagemBase64 = function(imagem) {
      return 'data:' + consultarExtencao(imagem.extencao) + ';base64,' + imagem.binario;
    };


    var consultarExtencao = function(extencao) {
      if (extencao == 'image/png' || extencao == '.png') {
        return 'image/png';
      } else if (extencao == 'image/tif' || extencao == '.tif') {
        return 'image/tif';
      } else if (extencao == 'image/jpeg' || extencao == '.jpeg') {
        return 'image/jpeg';
      } else if (extencao == 'image/jpg' || extencao == '.jpg') {
        return 'image/jpg';
      }
    };

    return {
      apagar: apagar,
      uploadArquivo: uploadArquivo,
      gerarPreviewImagens: gerarPreviewImagens,
      gerarSRCImagemBase64: gerarSRCImagemBase64,
      consultarExtencao: consultarExtencao
    };

  }

  angular
    .module('su-integrador')
    .factory('arquivoServiceIntegrador', service);
})();

/**
 * Created by Basis Tecnologia on 15/06/2016.
 */
(function () {
    'use strict';


angular.module("su-integrador").filter('cpfcnpj', function() {
    return function(input){
        if (!input) {
            return;
        } else if(input.toString().length <= 11 && input.toString().length > 3) {
            while(input.toString().length < 11){ input = '0'+input }
            return input.toString().substr(0,3) + '.' + input.toString().substr(3,3) + '.' + input.toString().substr(6,3) + '-' + input.toString().substr(9,2);
        }
        else {
            while(input.toString().length < 14){ input = '0'+input }
            return input.toString().substr(0,2) + '.' + input.toString().substr(2,3) + '.' + input.toString().substr(5,3) + '/' + input.toString().substr(8,4) + '-' + input.toString().substr(12,2);
        }
    };
});


})();

(function () {

  'use strict';

  angular.module('su-integrador').config(["$stateProvider", "$urlRouterProvider", function ($stateProvider, $urlRouterProvider) {

    $urlRouterProvider.otherwise('/integrador');

    $stateProvider
      .state('INTEGRADOR', {
        abstract: true,
        template: '<div id="su-integrador" ui-view></div>',
        url: '/integrador',
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/app/language');
          }]
        }
      })
      .state('INTEGRADOR.HOME', {
        url: '',
        templateUrl: 'scripts-integrador/pages/home/view/home.tmpl.html',
        controller: 'HomeIntegradorController as homeCtrl',
        ncyBreadcrumb: {
          label: 'HOME'
        }
      })
      .state('INTEGRADOR.INFORMES', {
        url: '/informes',
        templateUrl: 'scripts-integrador/pages/informes/view/consultarInformes.html',
        controller: 'informesController as informesCtrl',
        ncyBreadcrumb: {
          label: 'INFOMES',
          parent: 'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/informes/language');
          }]
        }
      })
      .state('INTEGRADOR.CADASTRAR_INFORME', {
        url: '/cadastroInformes',
        templateUrl: 'scripts-integrador/pages/informes/view/cadastroInformes.html',
        controller: 'cadastroInformesController as cadastroInformesCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRAR INFORME',
          parent: 'INTEGRADOR.INFORMES'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/informes/language');
          }]
        }
      })
      .state('INTEGRADOR.ATOS_E_CONTRATOS', {
        url: '/cadastroContratoTermo',
        templateUrl: 'scripts-integrador/pages/atos-contratos/view/cadastroContratoTermo.html',
        controller: 'cadastroContratoTermoController as cadastroContratoTermoCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRAR ATOS E CONTRATOS',
          parent: 'INTEGRADOR.CONSULTAR_ATOS_E_CONTRATOS'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/atos-contratos/language');
          }]
        }
      })
      .state('INTEGRADOR.CONSULTAR_ATOS_E_CONTRATOS', {
        url: '/consultarContratoTermo',
        templateUrl: 'scripts-integrador/pages/atos-contratos/view/consultarAtosContratosTermo.html',
        controller: 'consultarAtosContratosTermoController as consultarContratoTermoCtrl',
        ncyBreadcrumb: {
          label: 'CONSULTAR ATOS E CONTRATOS',
          parent: 'INTEGRADOR.HOME'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/atos-contratos/language');
          }]
        }
      })
      .state('INTEGRADOR.EDITAR_ATOS_E_CONTRATOS', {
        url: '/alterarContratoTermo?idContrato',
        templateUrl: 'scripts-integrador/pages/atos-contratos/view/cadastroContratoTermo.html',
        controller: 'cadastroContratoTermoController as cadastroContratoTermoCtrl',
        ncyBreadcrumb: {
          label: 'EDITAR ATOS E CONTRATOS',
          parent: 'INTEGRADOR.CONSULTAR_ATOS_E_CONTRATOS'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/atos-contratos/language');
          }]
        }
      })
      .state('INTEGRADOR.MODELO_CONTRATO', {
        url: '/modeloContrato?idContrato',
        templateUrl: 'scripts-integrador/pages/atos-contratos/view/modeloContrato.html',
        controller: 'previewController as previewCtrl',
        ncyBreadcrumb: {
          label: 'MODELO CONTRATO',
          parent: 'INTEGRADOR.ATOS_E_CONTRATOS'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/atos-contratos/language');
          }]
        }
      })
      .state('INTEGRADOR.MODELO_CERTIDAO', {
        url: '/modeloCertidao?idContrato',
        templateUrl: 'scripts-integrador/pages/atos-contratos/view/modeloCertidao.html',
        controller: 'previewController as previewCtrl',
        ncyBreadcrumb: {
          label: 'MODELO CERTIDAO',
          parent: 'INTEGRADOR.ATOS_E_CONTRATOS'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/atos-contratos/language');
          }]
        }
      })
      .state('INTEGRADOR.SIAPA', {
        url: '/siapa?url',
        templateUrl: 'scripts-integrador/pages/siapa/view/siapa.tmpl.html',
        controller: 'SiapaController as siapaCtrl',
        ncyBreadcrumb: {
          label: 'SIAPA',
          parent: 'INTEGRADOR.HOME'
        },
        params: {
          urlMenu:null
        }
      })
      .state('INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE', {
        url: '/consultaInstrumentoFuncionalidade',
        templateUrl: 'scripts-integrador/pages/instrumento-funcionalidade/view/consultaInstrumentoFuncionalidade.html',
        controller: 'consultaInstrumentoFuncionalidadeController as consultaInstrumentoFuncionalidadeCtrl',
        ncyBreadcrumb: {
          label: 'CONSULTAR INSTRUMENTO FUNCIONALIDADE',
          parent: 'INTEGRADOR.HOME'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/instrumento-funcionalidade/language');
          }]
        }
      })
      .state('INTEGRADOR.CADASTRAR_INSTRUMENTO_FUNCIONALIDADE', {
        url: '/cadastroInstrumentoFuncionalidade',
        templateUrl: 'scripts-integrador/pages/instrumento-funcionalidade/view/cadastroInstrumentoFuncionalidade.html',
        controller: 'cadastroInstrumentoFuncionalidadeController as cadastroInstrumentoFuncionalidadeCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRAR INSTRUMENTO FUNCIONALIDADE',
          parent: 'INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/instrumento-funcionalidade/language');
          }]
        }
      })

      .state('INTEGRADOR.EDITAR_INSTRUMENTO_FUNCIONALIDADE', {
        url: '/editarInstrumentoFuncionalidade/',
        templateUrl: 'scripts-integrador/pages/instrumento-funcionalidade/view/cadastroInstrumentoFuncionalidade.html',
        controller: 'cadastroInstrumentoFuncionalidadeController as cadastroInstrumentoFuncionalidadeCtrl',
        ncyBreadcrumb: {
          label: 'EDITAR INSTRUMENTO FUNCIONALIDADE',
          parent: 'INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/instrumento-funcionalidade/language');
          }]
        }
      })


      .state('INTEGRADOR.CADASTRO_DOCUMENTO',{
        url: '/cadastrarDocumento',
        templateUrl: 'scripts-integrador/pages/documento/view/cadastroDocumento.html',
        controller: 'cadastroDocumentoController as cadastroDocumentoCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRAR DOCUMENTO',
          parent: 'INTEGRADOR.CONSULTAR_DOCUMENTO_PESSOA'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/documento/language');
          }]
        }
      })

      .state('INTEGRADOR.EDITAR_DOCUMENTO',{
        url: '/editarDocumento',
        templateUrl: 'scripts-integrador/pages/documento/view/cadastroDocumento.html',
        controller: 'cadastroDocumentoController as cadastroDocumentoCtrl',
        ncyBreadcrumb: {
          label: 'EDITAR DOCUMENTO',
          parent: 'INTEGRADOR.CONSULTAR_DOCUMENTO_PESSOA'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/documento/language');
          }]
        }
      })

      .state('INTEGRADOR.CONSULTAR_DOCUMENTO_PESSOA',{
        url: '/consultarDocumento',
        templateUrl: 'scripts-integrador/pages/documento/view/consultaDocumentosPessoa.html',
        controller: 'consultarDocumentoPessoaController as consultarDocumentoPessoaCtrl',
        ncyBreadcrumb: {
          label: 'CONSULTAR DOCUMENTO PESSOA',
          parent: 'INTEGRADOR.HOME'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/documento/language');
          }]
        }
      })

      .state('INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL',{
        url: '/fundamentoLegal',
        templateUrl: 'scripts-integrador/pages/fundamento-legal/view/consultarFundamentoLegal.html',
        controller: 'consultarFundamentoLegalController as consultarFundamentoLegalCtrl',
        ncyBreadcrumb: {
          label: 'CONSULTAR FUNDAMENTO LEGAL',
          parent: 'INTEGRADOR.HOME'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/fundamento-legal/language');
          }]
        }
      })

      .state('INTEGRADOR.CADASTRAR_FUNDAMENTO_LEGAL',{
        url: '/cadastrarFundamentoLegal',
        templateUrl: 'scripts-integrador/pages/fundamento-legal/view/cadastrarFundamentoLegal.html',
        controller: 'cadastrarFundamentoLegalController as cadastrarFundamentoLegalCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRAR FUNDAMENTO LEGAL',
          parent: 'INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/fundamento-legal/language');
          }]
        }
      })

      .state('INTEGRADOR.SUSPENDER_INSTRUMENTO',{
        url: '/suspenderInstrumentoFuncionalide',
        templateUrl: 'scripts-integrador/pages/instrumento-funcionalidade/view/partials/suspenderInstrumento.html',
        controller:'suspenderInstrumentoController as suspenderInstrumentoCtrl',
        ncyBreadcrumb: {
          label: 'SUSPENDER INSTRUMENTO FUNCIONALIDADE',
          parent: 'INTEGRADOR.CONSULTAR_INSTRUMENTO_FUNCIONALIDADE'
        },
        resolve:{
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/instrumento-funcionalidade/language')
          }]
        }
      })

      .state('INTEGRADOR.TEXTOS_AJUDA',{
        url: '/textosAjuda',
        templateUrl: 'scripts-integrador/pages/textos-ajuda/view/manutencaoTextosAjuda.html',
        controller: 'textosAjudaController as textosAjudaCtrl',
        ncyBreadcrumb: {
          label: 'MANUTENÇÃO TEXTOS AJUDA',
          parent: 'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/textos-ajuda/language');
          }]
        }
      })

      .state('INTEGRADOR.CADASTRO_TEXTOS_AJUDA',{
        url: '/cadastroTextosAjuda',
        templateUrl: 'scripts-integrador/pages/textos-ajuda/view/cadastroTextosAjuda.html',
        controller: 'textosAjudaController as textosAjudaCtrl',
        ncyBreadcrumb: {
          label: 'CADASTRO TEXTOS AJUDA',
          parent: 'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/textos-ajuda/language');
          }]
        }
      })

      .state('INTEGRADOR.EDITAR_TEXTOS_AJUDA',{
        url: '/edicaoTextosAjuda/:id',
        templateUrl: 'scripts-integrador/pages/textos-ajuda/view/cadastroTextosAjuda.html',
        controller: 'textosAjudaController as textosAjudaCtrl',
        ncyBreadcrumb: {
          label: 'EDIÇÃO TEXTOS AJUDA',
          parent: 'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/textos-ajuda/language');
          }]
        }
      })

      .state('error',{
        url: '/error',
        templateUrl: 'scripts-integrador/pages/geral/view/usuario-erro-login.tmpl',
      })

      .state('INTEGRADOR.CANCELAR_FUNDAMENTO_LEGAL',{
        url: '/cancelarFundamentoLegal',
        templateUrl: 'scripts-integrador/pages/fundamento-legal/view/partial/cancelarFundamentoLegal.html',
        controller: 'cancelarFundamentoLegalController as cancelarFundamentoLegalCtlr',
        ncyBreadcrumb:{
          label:'CANCELAR FUNDAMENTO LEGAL',
          parent:'INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/fundamento-legal/language');
          }]
        }

      })

      .state('INTEGRADOR.EDITAR_FUNDAMNETO_LEGAL',{
        url: '/editarFundamentoLegal',
        templateUrl: 'scripts-integrador/pages/fundamento-legal/view/cadastrarFundamentoLegal.html',
        controller: 'cadastrarFundamentoLegalController as cadastrarFundamentoLegalCtrl',
        ncyBreadcrumb: {
          label: 'EDITAR FUNDAMENTO LEGAL',
          parent: 'INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/fundamento-legal/language');
          }]
        }
      })

      .state('INTEGRADOR.DETALHAR_FUNDAMENTO_LEGAL',{
        url: '/detalharFundamentoLegal',
        templateUrl: 'scripts-integrador/pages/fundamento-legal/view/cadastrarFundamentoLegal.html',
        controller: 'cadastrarFundamentoLegalController as cadastrarFundamentoLegalCtrl',
        ncyBreadcrumb: {
          label: 'DETALHAR FUNDAMENTO LEGAL',
          parent: 'INTEGRADOR.CONSULTAR_FUNDAMENTO_LEGAL'
        },
        resolve:{
          trans:["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/fundamento-legal/language');
          }]
        }
      })

      .state('INTEGRADOR.CADASTRO_PROCEDIMENTO_OPERACIONAL_PADRAO',{
        url: '/cadastroProcedimentoOperacionalPadrao',
        templateUrl: 'scripts-integrador/pages/procedimento-operacional-padrao/view/procedimentoOperacionalPadrao.html',
        controller: 'procedimentoOperacionalPadraoController as popController',
        ncyBreadcrumb:{
          label:'CADASTRAR PROCEDIMENTO OPERACIONAL PADRÃO',
          parent:'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/procedimento-operacional-padrao/language');
          }]
        }

      })

      .state('INTEGRADOR.CADASTRO_ORIENTACAO_TECNICA',{
        url: '/orientacaoTecnica/cadastrar',
        templateUrl: 'scripts-integrador/pages/orientacao-tecnica/view/orientacaoTecnica.html',
        controller: 'orientacaoTecnicaController as orientacaoTecnicaController',
        ncyBreadcrumb:{
          label:'CADASTRAR ORIENTAÇÃO TÉCNICA',
          parent:'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/procedimento-operacional-padrao/language');
          }]
        }

      })

      .state('INTEGRADOR.CONSULTA_ORIENTACAO_TECNICA',{
        url: '/orientacaoTecnicas/consultar',
        templateUrl: 'scripts-integrador/pages/consulta-orientacao-tecnica/view/consultaOrientacaoTecnica.html',
        controller: 'consultaOrientacaoTecnicaController as consultaOrientacaoTecnicaController',
        ncyBreadcrumb:{
          label:'CONSULTAR ORIENTAÇÃO TÉCNICA',
          parent:'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/consulta-orientacao-tecnica/language');
          }]
        }

      })

      .state('INTEGRADOR.ORIENTACAO_TECNICA',{
        url: '/orientacaoTecnica/consultar/:id',
        templateUrl: 'scripts-integrador/pages/orientacao-tecnica/view/orientacaoTecnica.html',
        controller: 'orientacaoTecnicaController as orientacaoTecnicaController',
        ncyBreadcrumb:{
          label:'CONSULTAR ORIENTAÇÃO TÉCNICA',
          parent:'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/orientacao-tecnica/language');
          }]
        }

      })

      .state('INTEGRADOR.EDITAR_ORIENTACAO_TECNICA',{
        url: '/orientacaoTecnica/editar/:id',
        templateUrl: 'scripts-integrador/pages/orientacao-tecnica/view/orientacaoTecnica.html',
        controller: 'orientacaoTecnicaController as orientacaoTecnicaController',
        ncyBreadcrumb:{
          label:'EDITAR ORIENTAÇÃO TÉCNICA',
          parent:'INTEGRADOR.HOME'
        },
        resolve: {
          trans: ["Traducoes", function (Traducoes) {
            return new Traducoes('scripts-integrador/pages/orientacao-tecnica/language');
          }]
        }

      })

  }]);

})();

(function () {
  'use strict';

  angular.module('su-integrador')
    .value('urlPrefixo', '/integrador');

})();

angular.module("su-integrador").run(["$templateCache", function($templateCache) {$templateCache.put("scripts-integrador/app/language/pt-BR.json","{ \"btn-novo\": \"Novo\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-retornar\": \"Retornar\", \"btn-gravar\": \"Gravar\", \"btn-adiconar-documento\": \"Adicionar Documento\", \"btn-editar-documento\": \"Editar Documento\", \"btn-fechar\": \"Fechar\", \"btn-inserir\": \"Inserir\", \"btn-alterar\": \"Alterar\", \"btn-confirmar\": \"Confirmar\", \"btn-incluir\": \"Incluir\", \"btn-enviar\": \"Enviar\", \"rb-sim\": \"Sim\", \"rb-nao\": \"Não\", \"rb-atuar-processo\": \"Atuar processo\", \"rb-retornar-pendencia\": \"Retornar pendência para requerente\", \"rb-cancelar-requerimento\": \"Cancelar requerimento\", \"rb-processo-existente\": \"Processo existente\", \"rb-criar-novo-processo\": \"Criar novo processo\", \"label-descricao\": \"Descrição\", \"label-justificativa\": \"Justificativa\", \"label-nome\": \"Nome\", \"label-acoes\": \"Ações\", \"label-tipo\": \"Tipo\", \"label-avaliacao-tecnica\": \"Avaliação Técnica\", \"label-data\": \"Data\", \"label-situacao\": \"Situação\", \"label-responsavel\": \"Responsável\", \"label-justificativa-anotacao\": \"Justificativa/Anotação\", \"label-historico\": \"Histórico\", \"label-analise-previa-check-list\": \"Análise Prévia (Checklist)\", \"label-atesto-devidos-fins\": \"Atesto para os defidos fins que:\", \"label-documento-adicionados-requerimento\": \"Os documentos adicionados no requerimento enviado são considerados válidos para a abertura do processo\", \"label-obrigatorios\": \"Obrigatórios\", \"label-complementares\": \"Complementares\", \"label-numero-processo\": \"Nº processo\", \"label-anotacoes-complementares\": \"Anotações Complementares (caso necessário)\", \"label-denominacao\": \"Denominação das Partes\", \"label-uniao\": \"União\", \"label-terceiro\": \"Terceiro\", \"label-extensoes\": \"Extensões\", \"label-vinculo\": \"Vínculo\", \"tooltip-limpar-campos\": \"Limpar\", \"tooltip-pesquisar\": \"Pesquisar\", \"label-erro\": \"Erro\", \"label-utilizacao\": \"Utilização\", \"label-tipo-uso-permitido\": \"Tipo de Uso Permitido\", \"label-especificacao\": \"Especificação\", \"label-adicionar\": \"Adicionar\", \"label-acao\": \"Ação\", \"msg-validacao-justificativa\" : \"A justificativa é obrigatória quando é negado um documento ou análise.\", \"msg-campos-obrigatorios\" : \"Preencha os campos obrigatórios.\", \"msg-analise-salva-sucesso\" : \"Análise salva com sucesso.\", \"selecione-combo\": \"-- Selecione --\", \"erro-campos-obrigatorio\": \"Necessário preencher os dados obrigatórios.\", \"ver-campos\": \"Ver Campos\", \"ocultar-campos\": \"Ocultar Campos\", \"msg-excluir\": \"Deseja realmente excluir {{campo}}?\", \"msg-confirmar-exclusao\": \"Deseja confirmar exclusão?\", \"mensagem-campos-obrigatorios\":\"Necessário preencher os campos obrigatórios\", \"mensagem-sucesso-documentos\":\"Dados gravados com sucesso\" }");
$templateCache.put("scripts-integrador/app/directives/carousel/carousel.html","<div layout=column ng-show=\"imagens.length > 0\" class=carousel><div flex=100 layout=row><div flex=15 class=\"item volta\"><md-button ng-disabled=\"selectedIndex==0\" ng-click=retorna() class=md-icon-button><ng-md-icon icon=navigate_before size=30></ng-md-icon></md-button></div><div align=center flex=70 class=imagem><img ng-src={{imagens[selectedIndex]}}></div><div flex=15 class=\"item avanca\"><md-button ng-disabled=\"selectedIndex==imagens.length-1\" ng-click=avanca() class=md-icon-button><ng-md-icon icon=navigate_next size=30></ng-md-icon></md-button></div></div><div layout=row flex class=bolas><div align=center ng-repeat=\"i in imagens track by $index\"><ng-md-icon icon=\"{{$index==selectedIndex ? \'lens\':\'panorama_fisheye\'}}\" size=10></ng-md-icon></div></div></div><style>carousel{\n    heigh:500px;    \n}\ncarousel .item{\n    display: flex;\n    align-items: center;\n}\ncarousel .item.volta{\n    float:left;\n}\ncarousel .item.avanca{\n    float:right;\n}\ncarousel img{\n    width:80%;\n}\ncarousel .bolas{\n    margin: 0 auto;\n}</style>");
$templateCache.put("scripts-integrador/pages/atos-contratos/language/pt-BR.json","{ \"nome-ato-termo\": \"Nome do Ato/Termo\", \"label-data-cadastramento\": \"Data Cadastramento\", \"label-modulo\": \"Módulo\", \"label-tipo\": \"Tipo\", \"label-funcionalidade\": \"Instrumento/Funcionalidade\", \"label-prefixo\": \"Prefixo do código do ato\", \"label-despacho\": \"Selecionar despacho(s) vinculado(s) ao modelo\", \"label-competencia\": \"Competência para Assinatura\", \"possui-validade\": \"Possui Validade\", \"label-prazo\": \"Prazo(dias)\", \"label-clausulas\": \"Cláusulas\", \"label-conteudo\": \"conteúdo\", \"label-acoes\": \"Ações\", \"label-informe-conteudo\": \"Informe o conteúdo que irá compor o modelo\", \"label-fundamento\": \"fundamento\", \"label-preambulo\": \"Preâmbulo\", \"label-justificativa\": \"Justificativa\", \"label-checklist-configurar-componetes\": \"Configurar Componentes\", \"label-informacoes-compor-modelo\": \"Selecione a ordem e as informações e que irão compor o modelo\", \"label-contrato-termo\": \"Contrato/Termo\", \"label-ministerio-planejamento\": \"MINISTÉRIO DO PLANEJAMENTO, DESENVOLVIMENTO E GESTÃO\", \"label-secretaria-uniao\": \"SECRETARIA DO PATRIMÔNIO DA UNIÃO\", \"label-editar-modelo\": \"Editar modelo\", \"label-excluir-modelo\": \"Excluir modelo\", \"label-despacho\": \"Selecionar despacho(s) vinculados ao modelo\", \"fundamento-legal\": \"Fundamento Legal\", \"codigo-atendimento\": \"Código do Atendimento/Nº do Processo\", \"qualificacao-partes\": \"Qualificação das Partes\", \"uniao-intermedio\": \"União, por intermédio da Secretaria do Patrimônio da União, representado neste ato por\", \"tooltip-fechar\": \"Fechar\", \"tooltip-voltar\": \"Voltar\", \"tooltip-pre-visualizar\": \"Pré-Visualizar\", \"tooltip-salvar\": \"Salvar\", \"tooltip-config-componentes\": \"Configurar Componentes\", \"tooltip-adicionar\": \"Adicionar\", \"tooltip-limpar-campos\": \"Limpar campos\", \"tooltip-pesquiser-modelo\": \"Pesquisar modelo\", \"tooltip-novo-modelo\": \"Novo modelo\", \"btn-fechar\": \"fechar\", \"btn-pre-visualizacao\": \"pré-visualização\", \"btn-salvar\": \"salvar\", \"btn-config-componentes\": \"configurar componentes\", \"btn-adicionar\": \"adicionar\", \"btn-voltar\": \"Voltar\", \"clausula\": \"Cláusula\", \"clausula-identificador\": \"Clausula\", \"excluir\": \"Excluir\", \"editar-campo\": \"Editar {{campo}}\", \"excluir-campo\": \"Excluir {{campo}}\", \"descricao-modelo\": \"Informe o preambulo que irá compor o modelo:\", \"cpf\": \"cpf\", \"imovel\": \"Imóvel\", \"prazo\": \"prazo\", \"finalidade\": \"finalidade\", \"encargos\": \"encargo(s)\", \"preco-condicao-pagamento\": \"preço e condição de pagamento\", \"memorial-descritivo\": \"memorial descritivo\", \"responsavel-atual\": \"Responsável Atual\", \"texto-certidao\": \"Esta certidão/declaração é fornecida gratuitamente, e sua autenticidade poderá ser confirmada no endereço\", \"titulo-excluir-modelo\": \"Excluir Modelo\" }");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/cadastroContratoTermo.html","<md-content flex style=\"overflow: inherit !important\"><titulo-servico ng-if=menuAcessos></titulo-servico><form name=form ng-submit=\"validarFormulario(form) && enviarFormulario()\" novalidate><md-content flex style=\"overflow: inherit !important\"><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=85><label>{{\'nome-ato-termo\' | translate}}:</label><input ng-model=atoTermo.descricao ng-required=true name=\"{{\'nome-ato-termo\' | translate}}\" type=text></md-input-container><md-input flex style=\"text-align: right\"><h4 class=data-cadastramento>{{\'label-data-cadastramento\' | translate}}:</h4><md-datepicker ng-model=atoTermo.cadastramento disabled></md-datepicker></md-input></div><div layout=row layout-xs=column><md-input-container flex=20><label>{{\'label-tipo\' | translate}}:</label><md-select ng-model=atoTermo.tipoModeloContrato ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-tipo\' | translate}}\" ng-change=selecionarTipo()><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in tipoModelo | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=20 class=md-block><label>{{\'label-modulo\' | translate}}:</label><md-select ng-model=atoTermo.modulo ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=\"buscarFuncionalidadePorModulo(); limpaModulosEInstrumentos()\" name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in modulos | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=30><label>{{\'label-funcionalidade\' | translate}}:</label><md-select ng-model=atoTermo.instrumentoFuncionalidade ng-required=true ng-change=\"buscarDespachoPorFuncionalidade(); buscarFundamentoLegalFuncionalidade()\" ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-funcionalidade\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in funcionalidades | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=30><label>{{\'label-prefixo\' | translate}}:</label><input ng-model=atoTermo.prefixo maxlength=3 ng-pattern=/^[a-zA-Z]*$/ type=text capitalize ng-required=true></md-input-container></div><div layout=row layout-xs=column flex ng-if=atoTermo.instrumentoFuncionalidade><md-radio-group ng-model=atoTermo.despacho.id><label>{{\'label-despacho\' | translate}}:</label><md-radio-button ng-repeat=\"d in despacho | orderBy : \'id\'\" class=md-primary ng-value=d.id ng-disabled=d.selecionado flex-offset=5><label>{{d.descricao}}</label><md-tooltip ng-show=d.selecionado md-direction=top>Usado por {{d.nome}}</md-tooltip></md-radio-button></md-radio-group></div><div layout=row layout-xs=column flex=100 ng-if=atoTermo.instrumentoFuncionalidade><md-input-container flex><label>Fundamento legal</label><md-select ng-model=atoTermo.fundamentoLegal ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option value={{null}} ng-if=\"\'$value.id\' > 1\">{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in fundamentos | orderBy:\'descricao\'\" ng-value={{tipo}} ng-if=\"\'$value.id\' > 1\">{{removerHTML(tipo.descricao)}}</md-option><md-option ng-repeat=\"(index,tipo) in fundamentos | orderBy:\'descricao\'\" ng-value={{tipo}} ng-selected=\"index == 0\">{{removerHTML(tipo.descricao)}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex=100><md-input-container flex=60><label>{{\'label-competencia\' | translate}}:</label><md-select ng-model=atoTermo.tipocompetenciaAssinatura ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-competencia\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in tipoCompetenciaContrato | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container ng-if=\"atoTermo.tipoModeloContrato.id == TIPO_DECLARACAO_CERTIDAO\" flex=20 flex-offset=5><md-checkbox ng-model=atoTermo.validade class=md-primary>{{\'possui-validade\' | translate}}</md-checkbox></md-input-container><md-input-container ng-if=\"atoTermo.tipoModeloContrato.id == TIPO_DECLARACAO_CERTIDAO\n                                        && atoTermo.validade\" flex=20><label>{{\'label-prazo\' | translate}}:</label><input ng-model=atoTermo.prazo ng-required=atoTermo.validade name=\"{{\'label-prazo\' | translate}}\" type=number></md-input-container></div><span ng-if=atoTermo.tipoModeloContrato><md-content flex ng-if=\"atoTermo.tipoModeloContrato.id == TIPO_CONTRATO_TERMO\"><h3>{{\'label-denominacao\' | translate}}</h3><div layout=row layout-sm=column><md-input-container class=\"md-block sem-margem\" flex-gt-xs flex><label>{{\'label-uniao\' | translate}}</label><input ng-model=atoTermo.denominacaoPartes.uniao ng-required=true type=text></md-input-container><md-input-container class=\"md-block sem-margem\" flex-gt-xs flex><label>{{\'label-terceiro\' | translate}}</label><input ng-model=atoTermo.denominacaoPartes.terceiro ng-required=true type=text></md-input-container></div></md-content><md-content flex><md-tabs md-dynamic-height md-border-bottom><md-tab label=\"{{\'label-clausulas\' | translate | uppercase}}*\" ng-if=\"atoTermo.tipoModeloContrato.id == TIPO_CONTRATO_TERMO\"><md-content class=md-padding><adicionar-clausulas ng-model=atoTermo.clausulas></adicionar-clausulas></md-content></md-tab><md-tab label=\"{{\'label-conteudo\' | translate | uppercase}}*\" ng-if=\"atoTermo.tipoModeloContrato.id == TIPO_DECLARACAO_CERTIDAO\"><md-content class=md-padding><adicionar-editor-html ng-model=atoTermo.conteudos config=configConteudo></adicionar-editor-html></md-content></md-tab><md-tab label=\"{{\'label-preambulo\' | translate | uppercase}}*\"><md-content class=md-padding><adicionar-editor-html ng-model=atoTermo.preambulo config=configPreambulo></adicionar-editor-html></md-content></md-tab></md-tabs></md-content></span><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\" ng-show=atoTermo.tipoModeloContrato><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=fechar()><md-tooltip md-direction=bottom>{{\'tooltip-fechar\' | translate}}</md-tooltip>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised type=submit value=PRE_VISUALIZAR ng-click=\"acaoSubmit=PRE_VISUALIZAR\"><md-tooltip md-direction=bottom>{{\'tooltip-pre-visualizar\' | translate}}</md-tooltip>{{\'btn-pre-visualizacao\' | translate}}</md-button></section></div><!--- fim dos botoes --></md-content></form></md-content>");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/consultarAtosContratosTermo.html","<md-content flex><titulo-servico ng-if=menuAcessos></titulo-servico><form name=dataTableImovel><md-content flex><div layout=row><md-input-container flex=25><label>{{\'nome-ato-termo\' | translate}}</label><input ng-model=filtro.descricao></md-input-container><md-input-container flex=25><label>{{\'label-modulo\' | translate}}</label><md-select ng-model=filtro.modulo ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=buscarFuncionalidadePorModulo() name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in modulos | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=25><label>{{\'label-tipo\' | translate}}</label><md-select ng-model=filtro.tipoModeloContrato ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-tipo\' | translate}}\" ng-change=selecionarTipo()><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in tipoModelo | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=25><label>{{\'label-funcionalidade\' | translate}}</label><md-select ng-model=filtro.instrumentoFuncionalidade ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-funcionalidade\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in funcionalidades | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=limparCamposPesquisa()><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{LIMPAR}}</md-button><md-button class=md-raised ng-click=buscarDadosModelosPaginacao()><md-tooltip md-direction=bottom>{{\'tooltip-pesquiser-modelo\' | translate}}</md-tooltip>{{PESQUISAR}}</md-button><md-button class=md-raised ng-click=\"redirecionarNovoAtoContrato(); acaoSubmit=NOVO\"><md-tooltip md-direction=bottom>{{\'tooltip-novo-modelo\' | translate}}</md-tooltip>{{NOVO}}</md-button></section></div><!--- fim dos botoes --></md-content></form><div flex style=\"margin-top: 3%\"><div layout=column><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro>{{\'nome-ato-termo\' | translate}}</th><th md-column class=texto-centro>{{\'label-modulo\' | translate}}</th><th md-column class=texto-centro>{{\'label-tipo\' | translate}}</th><th md-column class=texto-centro>{{\'label-funcionalidade\' | translate}}</th><th md-column class=texto-centro>{{\'label-despacho\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"atosContratos in listaAtosContratos\"><td md-cell class=texto-centro-corpo><span ng-bind=atosContratos.descricao></span></td><td md-cell class=texto-centro-corpo><span ng-bind=atosContratos.modulo.descricao></span></td><td md-cell class=texto-centro-corpo><span ng-bind=atosContratos.tipoModeloContrato.descricao ng-click=\"acaoSubmit=EDITAR\"></span></td><td md-cell class=texto-centro-corpo><span ng-bind=atosContratos.instrumentoFuncionalidade.descricao ng-click=\"acaoSubmit=EDITAR\"></span></td><td md-cell class=texto-centro-corpo><span ng-bind=atosContratos.despacho.descricao></span></td><td md-cell class=texto-centro-corpo><div layout=row layout-align=\"center center\"><div layout-margin=5><a ng-href=\"#/integrador/alterarContratoTermo?idContrato={{atosContratos.id}}\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"{{\'label-editar-modelo\' | translate}} \"><md-tooltip>{{\'label-editar-modelo\' | translate}}</md-tooltip></ng-md-icon></a></div><div layout-margin=5><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-modelo\' | translate}}\" ng-click=\"modalConfirmarExclusaoAtos($event, atosContratos.id);\"><md-tooltip>{{\'label-excluir-modelo\' | translate}}</md-tooltip></ng-md-icon></div></div></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=buscarDadosModelosPaginacao md-page-select></md-table-pagination></md-conent></div></div></md-content>");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/modeloCertidao.html","<div flex=70 layout-align=center style=\"margin-left: 15%\"><div layout=row layout-align=\"center center\" class=brasao ng-init=\"brasao = \'assets/img/brasao.jpg\'\"><img ng-src={{brasao}}></div><h4 class=titulo-ministerio href=# editable-text=atoTermo.configuracaoAtosContratos[0].valorLabel>{{atoTermo.configuracaoAtosContratos[0].valorLabel || \'empty\'}} <i class=material-icons>mode_edit</i></h4><!--h4 class=\"titulo-ministerio\">{{\'label-secretaria-uniao\' | translate}}</h4--><h3 class=titulo>{{atoTermo.descricao}} / {{formmatarCodigoGerado(codigoGerado)}}</h3><div><md-content flex><md-table-container><table md-table><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"componentes in dictConfigurarComponente\"><td md-cell class=\"celula tamanho-setas\" style=\"border-top: 0% !important; height: 0% !important\"><div layout=column flex=10><i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index-1, dictConfigurarComponente)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index+1, dictConfigurarComponente)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div ng-if=\"item.hierarquia == \'PRIMARIO\'\" ng-repeat=\"item in componentes.item\"><div layout=row class=titulo-componente style=\"height: 30px\"><div ng-hide=!item.status><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox></div><h2 href=# editable-text=item.descricao ng-hide=\"item.identificador == \'COMPETENCIA_ASSINATURA\'\" style=\"margin-top: 5px\">{{item.descricao}} <i class=material-icons>mode_edit</i></h2><h2 ng-hide=\"item.identificador != \'COMPETENCIA_ASSINATURA\'\" style=\"margin-top: 5px; color: black\">{{item.descricao}}</h2></div></div><div class=\"alinhar-checkbox secundario\" ng-if=\"item.hierarquia == \'SECUNDARIO\'\" ng-repeat=\"item in componentes.item\" style=\"height: 20px !important\" layout=column><div layout=row><div ng-hide=\"item.identificador == \'VALOR_CONTEUDO\'\"><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox></div><h4 style=\"margin-top: 4px;margin-left: 5px\" ng-hide=\"item.identificador == \'VALOR_CONTEUDO\'\"><div href=# editable-text=item.descricao ng-bind-html=item.descricao><i class=material-icons>mode_edit</i></div></h4><h4 class=valor-conteudo ng-hide=\"item.identificador != \'VALOR_CONTEUDO\'\"><div ng-bind-html=item.descricao></div></h4><h4 href=# editable-text=item.label style=\"font-weight: normal !important;\n                                    margin-top: 5px\" ng-class=\"{espacoLabel: item.descricao}\">{{item.label}} <i class=material-icons>mode_edit</i></h4></div><div layout=row><div class=\"alinhar-checkbox terciario\" ng-if=\"itemTerciario.hierarquia == \'TERCIARIO\'\" ng-repeat=\"itemTerciario in item.item\" style=\"height: 20px\" layout=row><md-checkbox ng-checked=itemTerciario.status class=md-primary ng-click=\"itemTerciario.status = !itemTerciario.status\"></md-checkbox><h4 href=# editable-text=itemTerciario.descricao style=\"margin-top: 4px\">{{itemTerciario.descricao}}: <i class=material-icons>mode_edit</i></h4></div></div></div></td></tr></tbody></table></md-table-container><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\" ng-show=atoTermo.tipoModeloContrato><md-button class=md-raised ng-click=voltar()><md-tooltip md-direction=bottom>{{\'tooltip-voltar\' | translate}}</md-tooltip>{{\'btn-voltar\' | translate}}</md-button><md-button class=md-raised name=salvar ng-click=salvar()><md-tooltip md-direction=bottom>{{\'tooltip-salvar\' | translate}}</md-tooltip>{{\'btn-salvar\' | translate | uppercase}}</md-button></div><!--- fim dos botoes --></md-content></div></div>");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/modeloContrato.html","<div flex=70 layout-align=center style=\"margin-left: 15%\"><div layout=row layout-align=\"center center\" class=brasao ng-init=\"brasao = \'assets/img/brasao.jpg\'\"><img ng-src={{brasao}}></div><h4 class=titulo-ministerio href=# editable-text=tituloMinisterio>{{tituloMinisterio}} <i class=material-icons>mode_edit</i></h4><!--h4 class=\"titulo-ministerio\">{{\'label-secretaria-uniao\' | translate}}</h4--><h3 class=titulo>{{\'label-contrato-termo\' | translate | uppercase}} / {{formmatarCodigoGerado(codigoGerado)}}</h3><div><div layout=row><div flex=60></div><div flex=40><h4 style=\"text-align:justify; text-justify: inter-word\">{{atoTermo.descricao}}</h4></div></div><md-content flex class=modelo-contrato><md-content style=\"padding-left: 0px !important\"><label ng-bind-html=atoTermo.preambulo.descricao></label><div layout=row class=fundamento-legal><label>{{\'fundamento-legal\' | translate}}:</label>{{removerHTML(atoTermo.fundamentoLegal.descricao)}}</div><div layout=row class=codigo-atendimento><label>{{\'codigo-atendimento\' | translate}}:</label>[Código do Atendimento]/[Nº do Processo]</div></md-content><md-table-container><table md-table><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"componentes in dictConfigurarComponente\"><td md-cell class=\"celula tamanho-setas\" style=\"border-top: 0% !important; height: 0% !important\"><div layout=column flex=10><i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index-1, dictConfigurarComponente)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index+1, dictConfigurarComponente)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div ng-if=\"item.hierarquia == \'PRIMARIO\'\" ng-repeat=\"item in componentes.item\"><div layout=row class=titulo-componente style=\"height: 30px\"><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox><h2 href=# editable-text=item.descricao style=\"margin-top: 5px\" ng-class=\"{grey: !item.status}\">{{item.descricao}} <i class=material-icons>mode_edit</i></h2></div><h4 href=# editable-text=item.label class=componentes-complemento>{{item.label}} <i class=material-icons>mode_edit</i></h4></div><div class=alinhar-checkbox ng-if=\"item.hierarquia == \'SECUNDARIO\'\" ng-repeat=\"item in componentes.item\" layout=column><div layout=column><div layout=row><div ng-hide=\"item.descricao == \'CLAUSULAS\'\"><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox></div><h4 href=# editable-text=item.descricao ng-hide=\"item.descricao == \'CLAUSULAS\'\" ng-class=\"{grey: !item.status}\" class=secundario-titulo>{{item.descricao}}: <i class=material-icons>mode_edit</i></h4><h4 href=# editable-text=item.label ng-hide=\"item.descricao == \'CLAUSULAS\'\" ng-class=\"{grey: !item.status}\" class=secundario-label>{{item.label}} <i class=material-icons>mode_edit</i></h4><h4 ng-hide=\"item.descricao != \'CLAUSULAS\'\" class=clausulas>{{item.identificador}}:</h4><h4 ng-hide=\"item.descricao != \'CLAUSULAS\'\" class=clausulas-label>{{item.label}}</h4></div><div class=\"alinhar-checkbox terciario\" ng-if=\"itemTerciario.hierarquia == \'TERCIARIO\'\" ng-repeat=\"itemTerciario in item.item\" layout=row><md-checkbox ng-checked=itemTerciario.status class=md-primary ng-click=\"itemTerciario.status = !itemTerciario.status\"></md-checkbox><h4 ng-class=\"{grey: !itemTerciario.status}\" class=terciario-label>{{itemTerciario.descricao}}:</h4></div></div></div></td></tr></tbody></table></md-table-container><br><br><br><br>{{getLabel(\'dataGerada\', atoTermo.configuracaoAtosContratos)}}<br><br><br><br><md-content style=\"text-align: -webkit-center\"><div class=assinaturas layout=row layout-align=\"center center\" flex=20>{{atoTermo.denominacaoPartes.uniao}}</div><div layout=row layout-align=\"center center\"><label>UNIÃO-[nome do representante(de quem assina)]</label></div></md-content><md-content style=\"text-align: -webkit-center\"><div class=assinaturas layout=row layout-align=\"center center\" flex=20>{{atoTermo.denominacaoPartes.terceiro}}</div><div layout=row layout-align=\"center center\"><label>[nome do representante(do CNPJ)]</label></div></md-content><md-content style=\"text-align: -webkit-center\"><div class=assinaturas layout=row layout-align=\"center center\" flex=20>{{atoTermo.denominacaoPartes.terceiro}}</div><div layout=row layout-align=\"center center\"><label>[nome da Pessoa 1)]</label></div></md-content><md-content style=\"text-align: -webkit-center\"><div class=assinaturas layout=row layout-align=\"center center\" flex=20>{{atoTermo.denominacaoPartes.terceiro}}</div><div layout=row layout-align=\"center center\"><label>[nome do conjuge da Pessoa(se houver)]</label></div></md-content><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\" ng-show=atoTermo.tipoModeloContrato><md-button class=md-raised ng-click=voltar()><md-tooltip md-direction=bottom>{{\'tooltip-voltar\' | translate}}</md-tooltip>{{\'btn-voltar\' | translate}}</md-button><md-button class=md-raised name=salvar ng-click=salvar()><md-tooltip md-direction=bottom>{{\'tooltip-salvar\' | translate}}</md-tooltip>{{\'btn-salvar\' | translate | uppercase}}</md-button></div><!--- fim dos botoes --></md-content></div></div>");
$templateCache.put("scripts-integrador/pages/consulta-orientacao-tecnica/language/pt-BR.json","{ \"label-titulo\": \"Título\", \"label-area\": \"Área\", \"label-vigente\": \"Vigente\", \"label-assunto\": \"Assunto\", \"button-editar\": \"Editar\", \"button-deletar\": \"Deletar\", \"button-visualizar\": \"Visualizar\" }");
$templateCache.put("scripts-integrador/pages/consulta-orientacao-tecnica/view/consultaOrientacaoTecnica.html","<titulo-servico class=texto-principal></titulo-servico><form name=dataTableImovel><md-content flex><div layout=row><md-input-container flex><label>{{\'label-titulo\' | translate}}</label><input ng-model=filtro.titulo></md-input-container></div><div layout=row><md-input-container flex><label>{{\'label-area\' | translate}}</label><md-select ng-model=filtro.listAreas multiple md-on-close=clearSearchArea()><md-select-header class=integradorSelectSearch><input ng-model=searchArea type=search placeholder=\"Pesquisar Área\" class=\"demo-header-searchbox md-text\"></md-select-header><md-optgroup><md-option ng-value=area.descricao ng-repeat=\"area in areasOrientacaoTecnica | filter:searchArea\">{{area.descricao}}</md-option></md-optgroup></md-select></md-input-container><md-input-container flex><label>{{\'label-vigente\' | translate}}</label><md-select ng-model=filtro.vigente><md-option ng-value=true>Ativo</md-option><md-option ng-value=false>Suspenso</md-option></md-select></md-input-container></div><div layout=row><md-input-container flex=50><label>{{\'label-assunto\' | translate}}</label><md-select ng-model=filtro.listAssuntos multiple md-on-close=clearSearchAssunto()><md-select-header class=integradorSelectSearch><input ng-model=searchAssunto type=search placeholder=\"Pesquisar Assunto\" class=\"demo-header-searchbox md-text\"></md-select-header><md-optgroup><md-option ng-value=assunto.descricao ng-repeat=\"assunto in assuntosOrientacaoTecnica | filter:searchAssunto\">{{assunto.descricao}}</md-option></md-optgroup></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=clearFilter()><md-tooltip md-direction=bottom>{{\'toltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate }}</md-button><md-button class=md-raised ng-click=pesquisar()><md-tooltip md-direction=bottom>{{\'toltip-pesquiser-servicos\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate }}</md-button><md-button class=md-raised ng-click=novoCadastro()><md-tooltip md-direction=bottom>{{\'toltip-adicionar-servicos\' | translate}}</md-tooltip>{{\'btn-novo\' | translate }}</md-button></section></div><!--- fim dos botoes --></md-content></form><div flex style=\"margin-top: 3%\" class=custom-style><div layout=column><md-conent flex><md-table-container><table md-table ng-model=vm.imovelSelecionado><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro>Título</th><th md-column class=texto-centro>Área</th><th md-column class=texto-centro style=\"text-align: center !important\">Assunto</th><th md-column class=texto-centro style=\"text-align: center !important\">Publicar</th><th md-column class=texto-centro style=\"text-align: center !important\">Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaOrientacaoTecnica\"><td md-cell class=texto-centro-corpo><span ng-bind=item.titulo></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.area></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.assunto></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.publicado></span></td><td md-cell class=texto-centro-corpo style=\"display: block; position: relative\"><md-fab-speed-dial md-direction=left style=\"margin-right: 50%; margin-top: -3px; transform: translateX(20%)\" class=\"hand md-scale alinhar-md-fab-speed-dial\" layout-align=\"end end\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><a href=#/integrador/orientacaoTecnica/editar/{{item.id}} ng-click=editarPop($index) ng-if=!consultar><ng-md-icon class=icon-subacao icon=create style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-editar\' | translate}}</md-tooltip></ng-md-icon></a><a href=#/integrador/orientacaoTecnica/consultar/{{item.id}}><ng-md-icon class=icon-subacao icon=search style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-visualizar\' | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto; background-color: #5bf869\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findPagination md-page-select></md-table-pagination></md-conent></div></div>");
$templateCache.put("scripts-integrador/pages/documento/language/pt-BR.json","{ \"label-nome-documento\":\"Nome do Documento\", \"label-extensao\":\"Extensões do documento\", \"label-pessoa-fisica\":\" Pessoa Fisíca\", \"label-pessoa-juridica\":\"Pessoa Jurídica\", \"label-imovel\":\"Imóvel\", \"label-adicionar-nova-extensao\":\"Adicionar Nova Extensão\", \"label-situacao\":\"Situação\", \"button-fechar\":\"Fechar\", \"button-limpar\":\"Limpar\", \"button-salvar\":\"Salvar\", \"button-novo\": \"Novo\", \"button-editar\": \"Editar\", \"button-pesquisar\": \"Pesquisar\", \"label-vinculo\":\"Vínculo\", \"title-consultar-documentos\": \"Consultar Documentos\" }");
$templateCache.put("scripts-integrador/pages/documento/view/cadastroDocumento.html","<md-content xmlns=http://www.w3.org/1999/html><h2 class=tituloFormulario>Documentos</h2><ng-form name=formCadastroDocumentos><md-dialog-content><md-input-container class=md-block flex-gt-xs flex=100><label>{{\'label-nome-documento\' |translate}}</label><input ng-model=documento.nome type=text name=nome required md-maxlength=1000 maxlength=1000></md-input-container></md-dialog-content><div><div style=\"margin-bottom: 10px\"><label>{{\'label-extensao\'| translate}}</label></div><div layout=row flex-offset=0 layout-aling=\"center center\"><div flex=10 ng-repeat=\"formatoArq in listaFormatosArquivos\"><md-checkbox class=md-primary ng-model=formatoArq.selecionado>{{ \'.\' + (formatoArq.formato | lowercase) }}</md-checkbox></div><div><md-checkbox class=md-primary ng-model=statusExtensaoDocumento>{{ \'Outro\' }}</md-checkbox></div></div></div><md-dialog-content ng-if=statusExtensaoDocumento><md-input-container class=md-block flex-gt-xs flex=100><label>{{\'label-adicionar-nova-extensao\' |translate}}</label><input ng-model=documento.outro ng-required=statusExtensaoDocumento type=text name=nome></md-input-container></md-dialog-content><md-dialog-content><div style=\"margin-bottom: 10px\"><label>{{\'label-vinculo\'| translate}}</label></div><div layout=row flex-offset=0 class=margin-top layout-wrap layout-aling=\"end start\"><div flex=15><md-checkbox class=md-primary ng-disabled=isEditar ng-model=documento.pessoaFisica><label>{{\'label-pessoa-fisica\' |translate}}</label></md-checkbox></div><div flex=15><md-checkbox class=md-primary ng-disabled=isEditar ng-model=documento.pessoaJuridica><label>{{\'label-pessoa-juridica\' |translate}}</label></md-checkbox></div><div flex=15><md-checkbox class=md-primary ng-disabled=isEditar ng-model=documento.imovel><label>{{\'label-imovel\' |translate}}</label></md-checkbox></div><md-input-container style=\"margin-top: 0px\" flex=50 class=md-block><label>{{\'label-situacao\'| translate}}</label><md-select required ng-model=documento.situacao><md-option ng-value=true>Ativo</md-option><md-option ng-value=false>Inativo</md-option></md-select></md-input-container></div></md-dialog-content><div style=margin-top:150px><section layout=row layout-sm=column layout-align=\"end center\" layout-wrap><md-button class=md-raised ng-click=fechar()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=md-raised ng-click=limparCampos()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=md-raised ng-click=validarFormulario()>{{\'button-salvar\' | translate}}</md-button></section></div></ng-form><md-content></md-content></md-content>");
$templateCache.put("scripts-integrador/pages/documento/view/consultaDocumentosPessoa.html","<md-content><h2 class=tituloFormulario>{{\'title-consultar-documentos\' | translate}}</h2><md-dialog-content><md-input-container class=md-block flex-gt-xs flex=100><label>{{\'label-nome-documento\' |translate}}</label><input ng-model=filtroConsulta.nome type=text name=nome md-maxlength=1000 maxlength=1000></md-input-container></md-dialog-content><md-dialog-content><label>{{\'label-vinculo\'| translate}}</label><div layout=row flex-offset=0 class=margin-top layout-wrap layout-aling=\"end start\"><div flex=20><md-checkbox class=md-primary ng-model=filtroConsulta.pessoaFisica><label>{{\'label-pessoa-fisica\' |translate}}</label></md-checkbox></div><div flex=20><md-checkbox class=md-primary ng-model=filtroConsulta.pessoaJuridica><label>{{\'label-pessoa-juridica\' |translate}}</label></md-checkbox></div><div flex=20><md-checkbox class=md-primary ng-model=filtroConsulta.imovel><label>{{\'label-imovel\' |translate}}</label></md-checkbox></div><md-input-container style=\"margin-top: 0px\" flex=40 class=md-block><label>{{\'label-situacao\'| translate}}</label><md-select ng-model=filtroConsulta.situacao><md-option ng-value=true>Ativo</md-option><md-option ng-value=false>Inativo</md-option></md-select></md-input-container></div></md-dialog-content><div style=margin-bottom:60px layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=redirecionarCadastrarDocumentosPessoa()><label>{{\'button-novo\' | translate}}</label></md-button><md-button class=md-raised ng-click=limparConsulta()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=md-raised ng-click=consultarDocumentosPessoa()><label>{{\'button-pesquisar\' | translate}}</label></md-button></section></div><md-content flex ng-show=\"paginadosDocumentosPessoa.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-nome\' | translate}}</th><th md-column>{{\'label-extensoes\' | translate}}</th><th md-column>{{\'label-vinculo\' | translate}}</th><th md-column>{{\'label-situacao\' | translate}}</th><th md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documentoPessoa in paginadosDocumentosPessoa\"><td md-cell><label ng-bind=documentoPessoa.nome></label></td><td md-cell><label ng-bind=exibirFormatosArquivoDocumento(documentoPessoa)></label><label ng-bind=exibirOutrosFormatos(documentoPessoa)></label></td><td md-cell><label ng-bind=documentoPessoa.vinculo></label></td><td md-cell><label ng-bind=validarSituacaoDocumento(documentoPessoa.situacao)></label></td><td md-cell><md-fab-speed-dial md-direction=left style=\"margin-right: 9%; margin-top: -14px\" class=\"hand md-scale alinhar-md-fab-speed-dial\" layout-align=\"end end\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><a href=\"\" ng-click=redirecionarEditarDocumentoPessoa(documentoPessoa.id)><ng-md-icon class=icon-subacao icon=create style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-editar\' | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginarDocumentosPessoa md-page-select></md-table-pagination></md-content></md-content>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/language/pt-BR.json","{ \"label-cadastrar-fundamento-legal\": \"Cadastrar Fundamento Legal\", \"label-consultar-fundamento-legal\": \"Consultar Fundamento Legal\", \"label-instrumento-funcionalidade\": \"Instrumento/Funcionalidade\", \"label-fundamento-legal\": \"Fundamento Legal\", \"label-descricao-fundamento-legal\": \"Descrição do Fundamento Legal\", \"label-status\": \"Status\", \"label-todos\": \"Todos\", \"label-cancelar-fundamento-legal\": \"Cancelar Fundamento Legal\", \"label-editar-fundamento-legal\":\"Editar Fundamento Legal\", \"msg-dado-cancelado-com-sucesso\": \"Dado cancelado com sucesso\", \"label-configuracoes\": \"Configurações\", \"label-utilizar-mesmas-configuracoes-fundamento\": \"Utilizar as mesmas configurações do Fundamento\", \"label-competencia\": \"Competência\", \"label-exigir-ato-autorizativo\": \"Exigir Ato Autorizativo\", \"label-superintendente\": \"Superintendente\", \"label-secretario\": \"Secretário\", \"label-ministro\": \"Ministro\", \"label-criterio-a-ser-utilizado\": \"Critério a ser utilizado\", \"label-apresenta-diferenca-por-regiao\": \"Apresenta diferença por região?\", \"label-apresenta-diferenca-por-natureza-urbano-rural\": \"Apresenta diferença por natureza (urbano/rural)?\", \"label-regioes\": \"Regiões\", \"label-natureza\": \"Natureza\", \"label-area-ha\": \"área (ha)\", \"label-valor-rs\": \"valor (R$)\", \"label-ou\": \"ou\", \"label-minimo\": \"Mínimo\", \"label-maximo\": \"Máximo\", \"label-todas\": \"Todas\", \"label-urbano\": \"Urbano\", \"label-rural\": \"Rural\", \"label-norte\": \"Norte\", \"label-nordeste\": \"Nordeste\", \"label-centro-oeste\": \"Centro Oeste\", \"label-sudeste\": \"Sudeste\", \"label-sul\": \"Sul\", \"btn-fechar\": \"Fechar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-novo\": \"Novo\", \"btn-editar\": \"Editar\", \"label-utilizacao\": \"Utilização\", \"label-tipo-uso-permitido\": \"Tipo de Uso Permitido\", \"label-especificacao\": \"Especificação\", \"label-adicionar\": \"Adicionar\", \"label-acao\": \"Ação\", \"label-responsavel-destinatario\": \"Responsável/Destinatário\", \"label-natureza-juridica-permitida\": \"Natureza Jurídica permitida\", \"label-inclur-area-fracao-utilizada-por-responsavel\": \"Incluir Área/Fração Utilizada por Responsável?\", \"label-permitir-imovel-ja-parcelado\":\"Permitir Imóvel já parcelado?\", \"label-sim\": \"Sim\", \"label-nao\": \"Não\", \"label-ativo\":\"Ativo\", \"label-inativo\":\"Inativo\", \"label-objeto-imovel-parcela\": \"Objeto (Imóvel/Parcela)\", \"label-permitir-imoveis-tipo-espelho-d-agua\": \"Permitir imóveis do tipo \\\"Espelho D\'água\\\"\", \"label-permitir-imoveis-tipo-cavidade-natural-subterranea\": \"Permitir imóveis do tipo \\\"Cavidade Natural Subterrânea\\\"\", \"label-natureza-imovel-permitido\": \"Natureza do Imóvel permitido\", \"label-situacao-incorporacao-imovel-permitido\": \"Situação da Incorporação do Imóvel permitido\", \"label-em-progresso-incorporacao\": \"Em processo de incorporação\", \"label-incorporado\": \"Incorporado\", \"label-nao-se-aplica\": \"Não se aplica\", \"label-forma-incorporacao-permitida\": \"Forma de Incorporação permitida\", \"label-tipo-aquisicao-permitida\": \"Tipo de Aquisição permitida\", \"label-entidade-extinta\": \"Entidade Extinta\", \"label-excluir\": \"Excluir\", \"label-avaliacao-imovel\": \"Avaliação do Imóvel\", \"label-necessario-avaliacao-imovel\": \"Necessário avaliação do imóvel\", \"label-tipo-avaliacao-necessaria\": \"Tipo de avaliação necessário\", \"label-avaliacao-terreno-referencia-pvg-spu\": \"Avaliação de Terreno - referência PVG/SPU\", \"label-avaliacao-terreno-referencia-valor-venal-municipio\": \"Avaliação de Terreno - referência Valor Venal/Município\", \"label-avaliacao-terreno-rural-referencia-incra\": \"Avaliação de Terreno Rural - referência INCRA\", \"btn-salvar\": \"Salvar\", \"label-detalhar\": \"Detalhar Fundamento Legal\", \"label-definicao-metodologia-avaliacao\": \"Definição da metodologia de Avaliação\", \"label-tipo-avaliacao\": \"Tipo de Avaliação\", \"label-definir-percentual-valor\": \"Definir percentual do valor\", \"label-valor-percentual\": \"Percentual do valor\", \"label-ordem-prioridade\": \"Ordem de Prioridade\", \"label-maior-valor\": \"Maior Valor\", \"btn-adicionar\": \"Adicionar\", \"label-tabela-ordem\": \"Ordem\", \"label-tabela-avaliacao\": \"Avaliação\", \"label-tabela-acoes\": \"Ações\", \"label-excluir-tipo-avaliacao\": \"Excluir\", \"erro-percentual-invalido\": \"Percentual inválido\" }");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/cadastrarFundamentoLegal.html","<ng-form name=formularioDadosFundamentoLegal novalidate><md-content flex><div id=formData layout=column><toaster-container></toaster-container><div layout=row flex><h2 class=tituloFormulario>{{isEditar ? \'label-editar-fundamento-legal\' : isDetalhar ? \'label-detalhar\' : \'label-cadastrar-fundamento-legal\' | translate}}</h2></div><div ng-if=\"!fundamentoLegal.ativo && fundamentoLegal.id\"><fieldset style=\"border-radius: 10px; padding: 2%; border-color: #80808040\"><p class=tituloFormulario>Detalhar Cancelamento do Fundamento Legal</p><div><b><span>{{\'label-responsavel\' | translate}}:</span></b> <span>{{dadosCancelamento.responsavel}}</span></div><div style=\"margin:1% 0 1% 0\"><b><span>{{\'label-data\' | translate}}:</span></b> <span>{{dadosCancelamento.dataSuspensao | date: \'dd/MM/yyyy\'}}</span></div><div style=\"margin-top: 2%\"><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=dadosCancelamento.justificativa name=\"{{\'label-justificativa\' | translate}}\" maxlength=500 md-maxlength=500 disabled></textarea></md-input-container></div></fieldset></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=100 class=md-block><label>{{\'label-instrumento-funcionalidade\'| translate}}</label><md-select ng-model=fundamentoLegal.idInstrumentoFuncionalidade required ng-disabled=\"isEditar || isDetalhar\"><md-option ng-repeat=\"instrumento in listaInstrumentosFuncionalidade\" ng-value=instrumento.instrumentoFuncionalidade.id>{{instrumento.instrumentoFuncionalidade.modulo.descricao + \' / \' + instrumento.instrumentoFuncionalidade.descricao + \' / \' + instrumento.titulo}}</md-option></md-select></md-input-container></div><label>{{\'label-fundamento-legal\' | translate}}</label><div layout=row><md-input-container flex=100 class=textangular><div text-angular required ng-disabled=true contenteditable=false ng-model=fundamentoLegal.template name=descricao ta-max-text=2000 ta-toolbar-button-class=\"btn btn-default\" ta-toolbar-button-active-class=active name=\"{{\'label-fundamento-legal\' | translate}}\"></div></md-input-container></div><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><label>{{\'label-descricao-fundamento-legal\' | translate}}:</label><textarea ng-disabled=isDetalhar ng-model=fundamentoLegal.descricao ui-mask-placeholder-char=space required name=\"{{\'label-descricao-fundamento-legal\' | translate}}\" md-maxlength=500 maxlength=500></textarea></md-input-container></div><div layout=row flex><h3>{{\'label-configuracoes\' | translate}}</h3></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=100 class=md-block><label>{{\'label-utilizar-mesmas-configuracoes-fundamento\'| translate}}</label><md-select ng-disabled=isDetalhar ng-model=fundamentoLegal.utilizarFundamentoLegal ng-model-options=\"{trackBy:\'$value.id\'}\" ng-change=utilizarFundamento()><md-option ng-value=\"\"></md-option><md-option ng-repeat=\"fundamentoCadastrado in listaFundamentosCadastrados\" ng-value=fundamentoCadastrado>{{fundamentoCadastrado.descricao}}</md-option></md-select></md-input-container></div><div layout=column layout-xs=column layout-sm=column flex=100><label style=\"margin-bottom: 1%\">{{\'label-competencia\'| translate}}</label><md-checkbox class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isExigirAtoAutorizativo name=\"{{\'label-exigir-ato-autorizativo\' | translate}}\" flex=25 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-exigir-ato-autorizativo\' | translate}}</label></md-checkbox><div layout=column layout-xs=column layout-sm=column flex=100 flex-offset=5 ng-if=fundamentoLegal.isExigirAtoAutorizativo><md-checkbox ng-required=\"!fundamentoLegal.isSecretario && !fundamentoLegal.isMinistro\" class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-superintendente\' | translate}}</label></md-checkbox><md-checkbox ng-required=\"!fundamentoLegal.isSuperintendente && !fundamentoLegal.isMinistro\" class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-secretario\' | translate}}</label></md-checkbox><md-checkbox ng-required=\"!fundamentoLegal.isSuperintendente && !fundamentoLegal.isSecretario\" class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-ministro\' | translate}}</label></md-checkbox></div></div><div ng-show=validarCriterioUtilizado() layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=33 class=md-block><label>{{\'label-criterio-a-ser-utilizado\'| translate}}</label><md-select ng-model=fundamentoLegal.criterio ng-required=validarCriterioUtilizado() ng-disabled=\"bloquear || isDetalhar\"><md-option ng-repeat=\"criterio in listaCriterios\" ng-value=criterio.nome>{{criterio.descricao}}</md-option></md-select></md-input-container><md-input-container style=\"margin-top: 0px\" flex=33 class=md-block><label>{{\'label-apresenta-diferenca-por-regiao\'| translate}}</label><md-select ng-model=fundamentoLegal.isDiferencaRegiao ng-disabled=\"bloquear || isDetalhar\" ng-required=validarCriterioUtilizado()><md-option ng-value=true>{{\'label-sim\' | translate}}</md-option><md-option ng-value=false>{{\'label-nao\' | translate}}</md-option></md-select></md-input-container><md-input-container style=\"margin-top: 0px\" flex=33 class=md-block><label>{{\'label-apresenta-diferenca-por-natureza-urbano-rural\'| translate}}</label><md-select ng-model=fundamentoLegal.isDiferencaNatureza ng-disabled=\"bloquear || isDetalhar\" ng-required=validarCriterioUtilizado()><md-option ng-value=true>{{\'label-sim\' | translate}}</md-option><md-option ng-value=false>{{\'label-nao\' | translate}}</md-option></md-select></md-input-container></div><div ng-if=\"validarCriterioUtilizado() && fundamentoLegal.criterio.length > 0 && fundamentoLegal.isDiferencaRegiao==configuracoesTodasTodas[0].isDiferencaRegiao && fundamentoLegal.isDiferencaNatureza==configuracoesTodasTodas[0].isDiferencaNatureza \" ng-include=\"\'scripts-integrador/pages/fundamento-legal/view/partial/configuracoesTodasTodas.html\'\"></div><div ng-if=\"validarCriterioUtilizado() && fundamentoLegal.criterio.length > 0 && fundamentoLegal.isDiferencaRegiao==configuracoesTodasNatureza[0].isDiferencaRegiao && fundamentoLegal.isDiferencaNatureza==configuracoesTodasNatureza[0].isDiferencaNatureza\" ng-include=\"\'scripts-integrador/pages/fundamento-legal/view/partial/configuracoesTodasNatureza.html\'\"></div><div ng-if=\"validarCriterioUtilizado() && fundamentoLegal.criterio.length > 0 && fundamentoLegal.isDiferencaRegiao==configuracoesRegiaoNatureza[0].isDiferencaRegiao && fundamentoLegal.isDiferencaNatureza==configuracoesRegiaoNatureza[0].isDiferencaNatureza\" ng-include=\"\'scripts-integrador/pages/fundamento-legal/view/partial/configuracoesRegiaoNatureza.html\'\"></div><div ng-if=\"validarCriterioUtilizado() && fundamentoLegal.criterio.length > 0 && fundamentoLegal.isDiferencaRegiao==configuracoesRegiaoTodas[0].isDiferencaRegiao && fundamentoLegal.isDiferencaNatureza==configuracoesRegiaoTodas[0].isDiferencaNatureza\" ng-include=\"\'scripts-integrador/pages/fundamento-legal/view/partial/configuracoesRegiaoTodas.html\'\"></div><incluir-utilizacao utilizacoes=fundamentoLegal.utilizacoes bloquear=bloquear is-detalhar=isDetalhar></incluir-utilizacao><br><div layout=column ng-if=\"moduloSelecionado==DESTINACAO\"><div layout=row flex><h3>{{\'label-responsavel-destinatario\' | translate}}</h3></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=50 class=md-block><label>{{\'label-natureza-juridica-permitida\'| translate}}</label><md-select ng-model=fundamentoLegal.naturezaJuridicaPermitidas ng-model-options=\"{trackBy:\'$value.descricao\'}\" multiple ng-disabled=\"bloquear || isDetalhar\" required><md-input-container style=\"margin: 0\"><md-checkbox ng-model=todosResponsaveis class=md-primary ng-change=selecionarTodosResponsavel(todosResponsaveis)>{{\'label-todos\' | translate}}</md-checkbox></md-input-container><md-option ng-repeat=\"natureza in listaNaturezasPermitidas\" ng-value=natureza>{{natureza.descricao}}</md-option></md-select></md-input-container></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input class=md-block flex layout=column><label>{{\'label-inclur-area-fracao-utilizada-por-responsavel\' | translate}}:</label><md-radio-group style=\"margin: 2% 0 0 2%\" class=md-block required ng-model=fundamentoLegal.isIncluirAreaFracaoResponsavel layout=row name=\"{{\'label-inclur-area-fracao-utilizada-por-responsavel\' | translate}}\" ng-disabled=\"bloquear || isDetalhar\"><md-radio-button class=md-primary ng-value=true><label>{{\'label-sim\' | translate}}</label></md-radio-button><md-radio-button class=md-primary ng-value=false><label>{{\'label-nao\' | translate}}</label></md-radio-button></md-radio-group></md-input></div><br><div layout=row flex><h3>{{\'label-objeto-imovel-parcela\' | translate}}</h3></div><div layout=column layout-xs=column layout-sm=column flex=100><md-checkbox class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isPermitirImoveisEspelhoDAgua name=\"{{\'label-permitir-imoveis-tipo-espelho-d-agua\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-permitir-imoveis-tipo-espelho-d-agua\' | translate}}</label></md-checkbox><md-checkbox class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isPermitirImoveisCavidadeNatural name=\"{{\'label-permitir-imoveis-tipo-cavidade-natural-subterranea\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-permitir-imoveis-tipo-cavidade-natural-subterranea\' | translate}}</label></md-checkbox></div><div layout=row flex-offset=0 class=margin-top style=\"margin-bottom: 2%\" layout-wrap><md-input class=md-block flex layout=column><label>{{\'label-permitir-imovel-ja-parcelado\' | translate}}:</label><md-radio-group style=\"margin: 2% 0 0 2%\" class=md-block required ng-model=fundamentoLegal.isPermitirImovelParcelado layout=row name=\"{{\'label-permitir-imovel-ja-parcelado\' | translate}}\" ng-disabled=\"bloquear || isDetalhar\"><md-radio-button class=md-primary ng-value=true><label>{{\'label-sim\' | translate}}</label></md-radio-button><md-radio-button class=md-primary ng-value=false><label>{{\'label-nao\' | translate}}</label></md-radio-button></md-radio-group></md-input></div><br><div layout=row layout-xs=column layout-sm=column flex=100><div layout=column flex=40><label style=\"margin-bottom: 2%\">{{\'label-natureza-imovel-permitido\' | translate}} *</label><div layout=row style=\"margin-left: 1.5%\"><md-checkbox ng-required=!fundamentoLegal.isNaturezaRural class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isNaturezaUrbana name=\"{{\'label-urbano\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-urbano\' | translate}}</label></md-checkbox><md-checkbox ng-required=!fundamentoLegal.isNaturezaUrbana class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isNaturezaRural name=\"{{\'label-rural\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-rural\' | translate}}</label></md-checkbox></div></div><div layout=column flex=60><label>{{\'label-situacao-incorporacao-imovel-permitido\' | translate}} *</label><div layout=row><md-checkbox ng-required=\"!fundamentoLegal.isincorporado && !fundamentoLegal.isNaoSeAplica\" class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isEmProcessoIncorporacao name=\"{{\'label-em-progresso-incorporacao\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-em-progresso-incorporacao\' | translate}}</label></md-checkbox><md-checkbox ng-required=\"!fundamentoLegal.isEmProcessoIncorporacao && !fundamentoLegal.isNaoSeAplica\" class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isincorporado name=\"{{\'label-incorporado\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-incorporado\' | translate}}</label></md-checkbox><md-checkbox ng-if=\"fundamentoLegal.isPermitirImoveisEspelhoDAgua || fundamentoLegal.isPermitirImoveisCavidadeNatural\" ng-checked=true class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.isNaoSeAplica name=\"{{\'label-nao-se-aplica\' | translate}}\" flex=100 ng-disabled=\"bloquear || isDetalhar\"><label>{{\'label-nao-se-aplica\' | translate}}</label></md-checkbox></div></div></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=50 class=md-block><label>{{\'label-forma-incorporacao-permitida\'| translate}}</label><md-select ng-model=fundamentoLegal.formaDeIncorporacaoPermitidas ng-model-options=\"{trackBy:\'$value.descricao\'}\" multiple required ng-disabled=\"bloquear || isDetalhar\"><md-input-container style=\"margin: 0\"><md-checkbox ng-model=todasFormasIncorporacao class=md-primary ng-change=selecionarTodasFormas(todasFormasIncorporacao)>{{\'label-todos\' | translate}}</md-checkbox></md-input-container><md-option ng-repeat=\"itemIncorporacao in listaIncorporacao\" ng-value=itemIncorporacao>{{itemIncorporacao.descricao}}</md-option></md-select></md-input-container><md-input-container style=\"margin-top: 0px\" flex=50 class=md-block ng-if=mostrarTipoAquisicao()><label>{{\'label-tipo-aquisicao-permitida\'| translate}}</label><md-select multiple ng-model=fundamentoLegal.tipoAquisicaoPermitidas ng-model-options=\"{trackBy:\'$value.descricao\'}\" required ng-disabled=\"bloquear || isDetalhar\"><md-input-container style=\"margin: 0\"><md-checkbox ng-model=todasTipoAquisicao class=md-primary ng-change=selecionarTodasTiposAquisicao(todasTipoAquisicao)>{{\'label-todos\' | translate}}</md-checkbox></md-input-container><md-option ng-repeat=\"itemAquisicao in listaAquisicao\" ng-value=itemAquisicao>{{itemAquisicao.descricao}}</md-option></md-select></md-input-container></div><div layout=row flex-offset=0 class=margin-top layout-wrap ng-if=mostrarEntidadeExtinta()><md-input-container style=\"margin-top: 0px\" flex=100 class=md-block><label>{{\'label-entidade-extinta\'| translate}}</label><md-select multiple ng-model=fundamentoLegal.entidadeExtintas ng-model-options=\"{trackBy:\'$value.descricao\'}\" required ng-disabled=\"bloquear || isDetalhar\"><md-input-container style=\"margin: 0\"><md-checkbox ng-model=todasEntidadesExtintas class=md-primary ng-change=selecionarTodasEntidadesExtintas(todasEntidadesExtintas)>{{\'label-todos\' | translate}}</md-checkbox></md-input-container><md-option ng-repeat=\"itemEntidade in listaEntidadesExtintas\" ng-value=itemEntidade>{{itemEntidade.descricao}}</md-option></md-select></md-input-container></div><br><div layout=row flex><h3>{{\'label-avaliacao-imovel\' | translate}}</h3></div><div layout=row flex-offset=0 class=margin-top layout-wrap style=\"margin-bottom: 20px !important\"><md-input class=md-block flex layout=column><label>{{\'label-definicao-metodologia-avaliacao\' | translate}} *</label><md-radio-group style=\"margin: 2% 0 0 2%\" class=md-block ng-model=fundamentoLegal.metodologiaAvaliacao layout=row name=\"{{\'label-inclur-area-fracao-utilizada-por-responsavel\' | translate}}\" ng-disabled=isDetalhar><md-radio-button class=md-primary ng-value=true><label>{{\'label-ordem-prioridade\' | translate}}</label></md-radio-button><md-radio-button class=md-primary ng-value=false><label>{{\'label-maior-valor\' | translate}}</label></md-radio-button></md-radio-group></md-input></div><div layout=row flex-offset=0 layout-wrap ng-show=\"fundamentoLegal.metodologiaAvaliacao == true || fundamentoLegal.metodologiaAvaliacao == false\"><md-input-container style=\"margin-top: 20px\" flex=50 class=md-block><label>{{\'label-tipo-avaliacao\'| translate}}</label><md-select ng-model=fundamentoLegal.tipoAvaliacao ng-disabled=isDetalhar><md-option ng-repeat=\"tipoAvaliacao in listaTiposAvaliacao\" ng-value=tipoAvaliacao>{{tipoAvaliacao.nome}}</md-option></md-select></md-input-container><md-input-container style=\"margin-top: 10px\" flex=20 class=md-block><md-button class=\"md-raised margin-botao-footer\" ng-click=adicionarTipoAvaliacao(fundamentoLegal.tipoAvaliacao) ng-disabled=isDetalhar>{{\'btn-adicionar\' | translate | uppercase}}</md-button></md-input-container></div><md-content ng-show=\"paginationFundamentosLegaisTipoAvaliacao.length > 0\"><md-table-container class=\"md-block margin-top-2-em\" flex-gt-xs flex=100><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row class=upload-head><th style=\"width: 30px\" md-column ng-show=\"fundamentoLegal.metodologiaAvaliacao == true && !isDetalhar\"></th><th style=\"width: 50px\" md-column ng-show=\"fundamentoLegal.metodologiaAvaliacao == true\">{{\'label-tabela-ordem\' | translate}}</th><th md-column>{{\'label-tabela-avaliacao\' | translate}}</th><th style=\"width: 5%\" md-column ng-show=!isDetalhar>{{\'label-tabela-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"fundamentoLegalTipoAvaliacao in paginationFundamentosLegaisTipoAvaliacao track by $index\"><td md-cell width=30px class=column-acao ng-show=\"fundamentoLegal.metodologiaAvaliacao == true && !isDetalhar\"><md-button class=\"md-fab md-mini button-acao button-acao-up\" ng-click=\"decrementarOrdem(paginationFundamentosLegaisTipoAvaliacao, fundamentoLegalTipoAvaliacao.ordem)\"><md-icon ng-bind=\"\'expand_less\'\" style=\"color: #000000\"></md-icon></md-button><md-button class=\"md-fab md-mini button-acao button-acao-down\" ng-click=\"incrementarOrdem(paginationFundamentosLegaisTipoAvaliacao, fundamentoLegalTipoAvaliacao.ordem)\"><md-icon ng-bind=\"\'expand_more\'\" style=\"color: #000000\"></md-icon></md-button></td><td md-cell ng-show=\"fundamentoLegal.metodologiaAvaliacao == true\">{{fundamentoLegalTipoAvaliacao.ordem}}</td><td md-cell>{{fundamentoLegalTipoAvaliacao.tipoAvaliacao.nome}}</td><td md-cell ng-show=!isDetalhar><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><a href=\"\" ng-click=removerTipoAvaliacao($index) class=\"md-fab md-mini\"><ng-md-icon icon=delete style=fill:#788489 size=25 alt=\"{{\'label-excluir-tipo-avaliacao\' | translate}}\"><md-tooltip>{{\'label-excluir-tipo-avaliacao\' | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" style=\"border: 1px solid #cccccc; margin:auto; height: 63px\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoTipoAvaliacao md-page-select></md-table-pagination></md-content><div layout=column layout-xs=column layout-sm=column flex=100><md-checkbox class=md-primary style=\"margin-left: 1%\" ng-model=fundamentoLegal.valorPercentual name=\"{{\'label-definir-percentual-valor\' | translate}}\" flex=100 ng-disabled=isDetalhar><label>{{\'label-definir-percentual-valor\' | translate}}</label></md-checkbox></div><div layout=row ng-show=fundamentoLegal.valorPercentual><md-input-container flex=50><label>{{\'label-valor-percentual\' | translate}}</label><input name=\"{{\'label-valor-percentual\' | translate}}\" ng-model=fundamentoLegal.percentual ui-number-mask=2 maxlength=6 ng-change=validaPercentual(fundamentoLegal.percentual) ng-required=\"fundamentoLegal.valorPercentual == true\" ng-disabled=isDetalhar><md-icon md-font-set=regular-font>%</md-icon></md-input-container></div></div></div><div class=button-botton layout=row layout-align=\"end center\"><md-button class=\"md-raised margin-botao-footer\" ng-click=fecharFundamentoLegal()>{{\'btn-fechar\' | translate | uppercase}}</md-button><md-button class=md-raised ng-show=!isDetalhar ng-click=salvarFundamentoLegal(formularioDadosFundamentoLegal)>{{\'btn-salvar\'| translate | uppercase}}</md-button></div></md-content></ng-form>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/consultarFundamentoLegal.html","<md-content><h2 class=tituloFormulario>{{\'label-consultar-fundamento-legal\' | translate}}</h2><md-dialog-content><md-input-container class=\"md-block margin-top-2-em\" flex-gt-xs flex=100><label>{{\'label-instrumento-funcionalidade\' | translate}}</label><md-select ng-model=filtro.idInstrumentoFuncionalidade ng-disabled=isEditar name=\"{{\'label-instrumento-funcionalidade\' | translate}}\"><md-option ng-repeat=\"instrumento in listaInstrumentosFuncionalidade\" ng-value=instrumento.instrumentoFuncionalidade.id>{{instrumento.instrumentoFuncionalidade.modulo.descricao + \' / \' + instrumento.instrumentoFuncionalidade.descricao + \' / \' + instrumento.titulo}}</md-option></md-select></md-input-container></md-dialog-content><md-dialog-content><md-input-container class=md-block flex-gt-xs flex=100><label>{{\'label-fundamento-legal\' |translate}}</label><textarea ng-model=filtro.template ui-mask-placeholder-char=space md-maxlength=100 maxlength=100 name=\"{{\'label-fundamento-legal\' | translate}}\"></textarea></md-input-container></md-dialog-content><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=voltar()>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised ng-click=limparFiltroConsulta()>{{\'btn-limpar\' | translate}}</md-button><md-button class=md-raised ng-click=pesquisarFundamentos()>{{\'btn-pesquisar\' | translate}}</md-button><md-button class=md-raised ng-click=cadastrarFundamento()>{{\'btn-novo\' | translate}}</md-button></section></div><!--- fim dos botoes --><md-content ng-show=\"listaFundamentosLegais.length > 0\"><md-table-container class=\"md-block margin-top-2-em\" flex-gt-xs flex=100><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-instrumento-funcionalidade\' | translate}}</th><th md-column>{{\'label-fundamento-legal\' | translate}}</th><th md-column>{{\'label-status\' | translate}}</th><th md-column>{{\'label-acao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"fundamento in listaFundamentosLegais\"><td md-cell><label ng-bind=\"fundamento.modulo + \'/\' + fundamento.instrumento\"></label></td><td md-cell><label ng-bind=fundamento.descricaoFundamento></label></td><td md-cell><label ng-bind=\"fundamento.ativo ? \'Ativo\' : \'Cancelado\'\"></label></td><td md-cell><md-fab-speed-dial style=\"left: -55px; position: relative\" md-direction=left class=\"hand md-scale alinhar-md-fab-speed-dial\" layout-align=\"end end\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><a href=\"\" ng-if=fundamento.ativo ng-click=redirecionarEditarFundamentoLegal(fundamento)><ng-md-icon class=icon-subacao icon=create style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'btn-editar\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-if=fundamento.ativo ng-click=cancelarFundamentoLegal(fundamento)><ng-md-icon class=icon-subacao icon=cancel style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'label-cancelar-fundamento-legal\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=detalharFundamentoLegal(fundamento)><ng-md-icon class=icon-subacao icon=search style=fill:#788489 size=25><md-tooltip>{{\"label-detalhar\" | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" style=\"border: 1px solid #cccccc; margin:auto; height: 63px\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarFundamentos md-page-select></md-table-pagination></md-content></md-content>");
$templateCache.put("scripts-integrador/pages/geral/view/header.html","<!-- Barra de navageção --><md-toolbar layout=row class=md-whiteframe-z3 ng-controller=\"MenuController as ul\"><!-- hide-gt-sm --><md-button class=menu ng-click=ul.toggleMenu() aria-label=\"Menu da Aplicação\"><ng-md-icon icon=menu style=fill:#fff size=38></ng-md-icon></md-button><div layout=column hide-xs layout-align=\"center center\"><a ui-sref=INTEGRADOR.HOME><img src=assets/img/logo-spu.png></a></div><div layout=row flex=75 layout-align=\"end center\"><a ui-sref=INTEGRADOR.HOME class=posicao-itens-barra-menu><ng-md-icon icon=home style=fill:#fff size=35 alt=Home><md-tooltip md-direction=bottom>Página inicial</md-tooltip></ng-md-icon></a><ng-md-icon icon=notifications class=posicao-itens-barra-menu size=35 alt=Notificações><md-tooltip md-direction=bottom>Notificações</md-tooltip></ng-md-icon><ng-md-icon icon=help class=posicao-itens-barra-menu size=35 alt=Ajuda><md-tooltip md-direction=bottom>Ajuda</md-tooltip></ng-md-icon><!--<a href=\"/api/saml/logout\">--><ng-md-icon icon=do_not_disturb class=posicao-itens-barra-menu size=35 alt=Notificações><md-tooltip md-direction=bottom>Logout</md-tooltip></ng-md-icon><!--</a>--><md-button class=\"md-fab md-primary md-hue-2\" ng-click=$mdMenu.open($event) aria-label=Configurações><md-tooltip md-direction=left>Configurações da aplicação</md-tooltip><md-icon>person</md-icon></md-button></div></md-toolbar><md-toolbar layout=row class=md-whiteframe-z3 style=\"min-height: 32px !important; background: #38813C !important\"><div flex=60 ncy-breadcrumb></div><!--<div flex=\"60\" style=\"text-align: right;\" >\n        <md-input-container style=\"width: 40%; text-align: center;margin-bottom: 1%;margin-top: 1%; font-size: large;\" class=\"favoritas\" layout-align=\"end center\">\n            <md-select style=\"padding-top: 0px;padding-bottom: 0px;\" ng-model=\"teste\" aria-label=\"tetsts\" placeholder=\"Funções favoritas\">\n                <md-option ng-value=\"favoritas\">Funções favoritas</md-option>\n            </md-select>\n        </md-input-container>\n    </div>\n    --><div flex layout=row layout-align=\"end center\"><contador-sessao></contador-sessao></div><!-- Container Menu e Conteúdo --></md-toolbar><!-- MENU --><menu>");
$templateCache.put("scripts-integrador/pages/geral/view/template.html","<div flex layout=column><!-- Conteúdo Principal --><div ng-include=\"\'scripts-integrador/pages/geral/view/header.html\'\"></div><div style=\"overflow:auto; height: 100% !important\"><md-content flex layout=column><div ui-view></div></md-content></div></div>");
$templateCache.put("scripts-integrador/pages/geral/view/usuario-erro-login.tmpl.html","<div class=padding-right-pagina><div class=md-title-com-borda>Ocorreu um erro ao acessar seus dados de login</div><h2 style=\"margin-top: 50px; text-align: center\">Erro de Autenticação!</h2><h3 style=\"text-align: center; line-height: 200%; margin-top: 50px\">Aguarde um momento e tente novamente.<br><br>Em caso de persistir o erro, favor encaminhar para o administrador</h3></div>");
$templateCache.put("scripts-integrador/pages/home/view/geovis.html","<div flex=100 id=pesquisa_barra layout-xs=column style=\"margin-top: 20px\"><div class=\"home-box pesquisa\"><header class=home-box-header><h2><b>Geovis</b></h2></header><div id=homeGeovis class=homeGeovis><div class=gv-map-overlay><form name=form-pesquisar layout=row><input name=busca ng-model=homeCtrl.buscaGeovis placeholder=Pesquisar flex> <a ng-click=homeCtrl.geovisPesquisar(); style=cursor:pointer><ng-md-icon icon=search></ng-md-icon></a></form></div><div id=homeMapGeovis class=homeMapGeovis></div></div></div></div>");
$templateCache.put("scripts-integrador/pages/home/view/graficos.html","<div><div style=\"margin-top: 20px\" flex-xs=100 flex class=\"home-box carteira-imobiliaria\"><header class=home-box-header><h2><b>CARTEIRA IMOBILIÁRIA</b></h2></header><div layout=row layout-xs=column layout-align-xs=\"center center\"><div flex=33 flex-xs=100><highchart id=graficoQuantidade config=homeCtrl.opcoesGraficoQuantidade></highchart></div><div flex=33 flex-xs=100><highchart id=graficoArea config=homeCtrl.opcoesGraficoArea></highchart></div><div flex=33 flex-xs=100><highchart id=graficoValor config=homeCtrl.opcoesGraficoValor></highchart></div></div></div></div><div layout=column layout-align=\"center end\"><md-button class=color-carteira-imobiliaria><span>+ Ver Painel</span><md-icon class=icon-forward-carteira-imobiliaria md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div>");
$templateCache.put("scripts-integrador/pages/home/view/home.tmpl.html","<div layout=column ng-cloak flex=100><md-content style=\"margin-top: 10px; background-color: #f7f7f7\"><div ng-include=\"\'scripts-integrador/pages/home/view/pendencias.html\'\"></div><div style=\"display: flex; flex-wrap: wrap; justify-content: space-between\"><div ng-include=\"\'scripts-integrador/pages/home/view/informes.html\'\" hide-xs hide-sm style=\"width: 65%\"></div><div ng-include=\"\'scripts-integrador/pages/home/view/informesMobile.html\'\" hide-md hide-gt-md></div><!-- Mapa Geovis --><div ng-include=\"\'scripts-integrador/pages/home/view/geovis.html\'\" class=ng-scope style=\"width: 35%;flex-grow: 1\"></div></div><div flex layout=row layout-gt-md=row layout-xs=column layout-md=column layout-gt-xs=column><div ng-include=\"\'scripts-integrador/pages/home/view/graficos.html\'\" flex=65 flex-xs=100 flex-md=100 flex-sm=100 flex-gt-xs=100 flex-gt-md=65></div><div ng-include=\"\'scripts-integrador/pages/home/view/painelServicos.html\'\" flex=35 flex-xs=100 flex-md=100 flex-sm=100 flex-gt-xs=100 flex-gt-md=35></div></div><div layout=row><div layout=column style=\"margin-top: 20px\"><!--<div layout=\"row\" layout-xs=\"column\">\n\n					<div layout=\"column\" layout-xs=\"column\" flex >\n						<div class=\"home-box msg\" style=\"height: 100%;\">\n							<header class=\"home-box-header\" style=\"width: 100%; \">\n								<h2 style=\"width: 100%;\">\n									<b>MENSAGENS</b>\n									<b style=\"float: right; margin-right: 28px; border: 1px black;\">\n										{{homeCtrl.quantidadeMensagensNaoLidas}}\n									</b>\n								</h2>\n							</header>\n							<section>\n								<md-content>\n									<p class=\"mensagem hover\" ng-repeat=\"item in homeCtrl.mensagens | limitTo : 11\"\n									   ng-model-options=\"{trackBy: \'$value.codigo\'}\"\n									   ng-click=\"homeCtrl.mensagemLida($index); homeCtrl.showMensagemDialog($event, $index)\">\n\n										<a ng-class=\"(item.lida == false ? \'msg-nao-lida\' : \'msg-lida\')\" href=\"#\" >\n											{{item.dataEnvio | date : \'dd/MM/yyyy\'}} - {{item.assunto}}\n										</a>\n									</p>\n								</md-content>\n							</section>\n\n						</div>\n						<div>\n							<md-button style=\"float: right;\" class=\"color-mensagens\" ng-click=\"homeCtrl.showMensagensDialog($event)\"><span>+ Mensagens</span>\n								<md-icon class=\"icon-forward-mensagens\" md-svg-src=\"assets/img/icons/arrow-point-to-right.svg\"></md-icon>\n							</md-button>\n						</div>\n\n					</div>\n\n				</div>--><!--<div flex layout=\"row\" >\n					<div  ng-include=\"\'scripts-integrador/pages/home/view/informes.html\'\"></div>\n					<div  ng-include=\"\'scripts-integrador/pages/home/view/pesquisaImovel.html\'\"></div>\n				</div>--><!--<div flex layout=\"row\">\n					<div ng-include=\"\'scripts-integrador/pages/home/view/graficos.html\'\"></div>\n					<div flex=\"35\" layout-xs=\"column\" style=\"margin-top: 20px; margin-left: 17px\">\n						<div class=\"home-box pesquisa\" flex>\n							<header class=\"home-box-header\">\n								<h2><b>CONSULTA DE SERVIÇOS</b></h2>\n							</header>\n							Teste\n						</div>\n					</div>\n				</div>--></div></div><!--<div style=\"margin-top: 20px;\" layout=\"row\" layout-xs=\"column\" flex>\n			<div layout=\"row\" layout-xs=\"column\" flex class=\"home-box videos-box\" >\n				<div layout=\"column\" layout-xs=\"column\" flex=\"100\" layout-xs=\"column\">\n					<header class=\"home-box-header\">\n						<h2><b>VÍDEOS</b></h2>\n					</header>\n					<md-content flex layout=\"row\" layout-xs=\"column\">\n						<div class=\"margem-3\"><iframe width=\"100%\" height=\"85\" src=\"https://www.youtube.com/embed/UQoD3W_Sp5s\" frameborder=\"0\" allowfullscreen></iframe></div>\n						<div class=\"margem-3\"><iframe width=\"100%\" height=\"85\" src=\"https://www.youtube.com/embed/KqnjNSVpps0\" frameborder=\"0\" allowfullscreen></iframe></div>\n						<div class=\"margem-3\"><iframe width=\"100%\" height=\"85\" src=\"https://www.youtube.com/embed/KqnjNSVpps0\" frameborder=\"0\" allowfullscreen></iframe></div>\n						<div class=\"margem-3\"><iframe width=\"100%\" height=\"85\" src=\"https://www.youtube.com/embed/7d0617fcJuk\" frameborder=\"0\" allowfullscreen></iframe></div>\n					</md-content>\n				</div>\n			</div>\n\n			<div layout=\"row\" layout-xs=\"column\" flex style=\"overflow-x: hidden;\">\n				<div layout=\"column\" layout-xs=\"column\" flex=\"100\" class=\"home-box links-uteis-box\" layout-xs=\"column\" style=\"overflow-x: hidden;\">\n					<header class=\"home-box-header\">\n						<h2><b>LINKS ÚTEIS</b></h2>\n					</header>\n					<md-content flex style=\"overflow-x: hidden;\" layout-xs=\"column\">\n						<div class=\"links-uteis-content\"><a href=\"http://www.planejamento.gov.br/\" target=\"_blank\"> <i class=\"fa fa-link fa-3x\" aria-hidden=\"true\"></i><p><strong>MP</strong></p></a></div>\n						<div class=\"links-uteis-content\"><a href=\"http://patrimoniodetodos.gov.br/\" target=\"_blank\"><i class=\"fa fa-link fa-3x\" aria-hidden=\"true\"></i><p><strong>SPU</strong></p></a></div>\n						<div class=\"links-uteis-content\"><a href=\"https://sei.mc.gov.br\" target=\"_blank\"><i class=\"fa fa-link fa-3x\" aria-hidden=\"true\"></i><p><strong>SEI</strong></p></a></div>\n						<div class=\"links-uteis-content\"><a href=\"https://sisrei.spu.planejamento.gov.br/sisrei/\" target=\"_blank\"><i class=\"fa fa-link fa-3x\" aria-hidden=\"true\"></i><p><strong>SISREI</strong></p></a></div>\n						<div class=\"links-uteis-content\"><a href=\"http://www.pgfn.fazenda.gov.br/\" target=\"_blank\"><i class=\"fa fa-link fa-3x\" aria-hidden=\"true\"></i><p><strong>PGFN</strong></p></a></div>\n\n					</md-content>\n\n				</div>\n			</div>\n\n\n		</div>--></md-content></div><div ng-if=urlServicos><iframe id=frame ng-src={{urlServicos}} width=1px style=\"display: none\"></iframe></div><script>setTimeout(function(){ $(\'#frame\').remove();}, 10000);</script>");
$templateCache.put("scripts-integrador/pages/home/view/informes.html","<div layout=row><div style=\"margin-top: 20px\" flex-xs=100 class=\"home-box infos\"><header class=home-box-header><h2 class=informe-header><b>INFORMES</b></h2></header><div><div layout=row><div layout=column flex=50 layout-margin=5 ng-repeat=\"item in homeCtrl.informesHome | limitTo : 1\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><div ng-if=item.imagem class=informe-caixa-img><img ng-src={{homeCtrl.gerarSrcImagem(item.imagem)}} height=150 width=300></div><div><div class=informe-data>{{item.dataPublicacao | date : \'dd/MM/yyyy\'}}</div><h2 class=informe-titulo>{{item.titulo}}</h2><div class=informe-descricao ng-bind-html=homeCtrl.descricaoExtensa(item.corpo)></div><div layout=row layout-align=\"end end\"><md-button class=btn-continue-lendo-primeira-noticia ng-click=\"homeCtrl.continuarLendo(item); homeCtrl.showInformesDialog($event)\">continue lendo</md-button></div></div></div><div layout=column flex=50 layout-margin=5><div layout=column flex=30 class=informe-item ng-repeat=\"item in homeCtrl.informesHome | limitTo : 2:1\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><div class=informe-data>{{item.dataPublicacao | date : \'dd/MM/yyyy\'}}</div><h2 class=informe-titulo>{{item.titulo}}</h2><div class=informe-descricao ng-bind-html=homeCtrl.descricaoExtensa(item.corpo)></div><div layout=row layout-align=\"end end\"><md-button class=btn-continue-lendo ng-click=\"homeCtrl.continuarLendo(item); homeCtrl.showInformesDialog($event)\">continue lendo</md-button></div></div></div></div><!-- <div layout=\"column\" flex=\"30\" class=\"informe-item\" ng-repeat=\"item in homeCtrl.informes | limitTo : 2\"\n            ng-model-options=\"{trackBy: \'$value.codigo\'}\" >\n           <p class=\"informe-data\">{{item.data | date : \'dd/MM/yyyy\'}}</p>\n           <h2 class=\"informe-titulo\">{{item.titulo}}</h2>\n           <p class=\"informe-descricao\">{{homeCtrl.descricaoExtensa(item.descricao)}}\n               <md-button class=\"btn-continue-lendo\"\n                          ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo\n               </md-button>\n           </p>\n\n       </div>\n--><!--  <div class=\"informe-item\" ng-repeat=\"item in homeCtrl.informes | limitTo : 3\"\n             ng-model-options=\"{trackBy: \'$value.codigo\'}\">\n            <p class=\"informe-data\">{{item.data | date : \'dd/MM/yyyy\'}}</p>\n            <h2 class=\"informe-titulo\">{{item.titulo}}</h2>\n            <p class=\"informe-descricao\">{{homeCtrl.descricaoExtensa(item.descricao)}}\n                <md-button class=\"btn-continue-lendo\"\n                           ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo\n                </md-button>\n            </p>\n\n        </div>--></div></div><!--  <div flex=\"35\" style=\"margin-top: 20px; margin-left:1%\" class=\"home-box pesquisa\">\n    <header class=\"home-box-header\">\n      <h2><b>PESQUISA DE IMÓVEL</b></h2>\n    </header>\n    <div flex=\"100\" class=\"pesquisa-img-container\">\n      <img src=\"/assets/img/mapa_imovel.png\" class=\"img-thumbnail img-circle img-responsive\">\n      <a href=\"https://imoveisfederais.planejamento.gov.br/spin-web/login\" target=\"_blank\">\n        <i class=\"fa fa-search fa-5x\" aria-hidden=\"true\" style=\"margin-top: 30%;\"></i>\n      </a>\n    </div>\n  </div> --><!--\n  <div layout=\"column\" layout-xs=\"column\" flex=\"65\" style=\"margin-right: 20px;\">\n      <div class=\"home-box infos\" style=\"width: 100% !important; height: 100%;\">\n          <header class=\"home-box-header\">\n              <h2 class=\"informe-header\"><b>INFORMES</b></h2>\n          </header>\n          <md-content style=\"height: 82%;\" class=\"no-scrollbar-y\">\n\n              <div class=\"informe-item\" ng-repeat=\"item in homeCtrl.informes | limitTo : 2\"\n                   ng-model-options=\"{trackBy: \'$value.codigo\'}\">\n                  <p class=\"informe-data\">{{item.data | date : \'dd/MM/yyyy\'}}</p>\n                  <h2 class=\"informe-titulo\">{{item.titulo}}</h2>\n                  <p class=\"informe-descricao\">{{homeCtrl.descricaoExtensa(item.descricao)}}\n                      <md-button class=\"btn-continue-lendo\"\n                                 ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo\n                      </md-button>\n                  </p>\n\n              </div>\n\n          </md-content>\n      </div>\n      <div>\n          <md-button style=\"float: right;margin-right: 0\" class=\"color-infos\" ng-click=\"homeCtrl.resetInformeSelecionado(); homeCtrl.showInformesDialog($event)\"><span>+ Informes</span>\n              <md-icon class=\"icon-forward-infos\" md-svg-src=\"assets/img/icons/arrow-point-to-right.svg\"></md-icon>\n          </md-button>\n      </div>\n  </div>--></div><div><div layout=column layout-align=\"end end\" layout-align=\"end end\"><md-button class=color-infos ng-click=\"homeCtrl.resetInformeSelecionado(); homeCtrl.showInformesDialog($event)\"><span>+ Informes</span><md-icon class=icon-forward-infos md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div><!--   <div flex=\"35\" layout=\"column\" style=\"margin-left:1%;\" layout-align=\"end end\"> --><!--     <md-button class=\"color-pesq-imoveis\" ng-href=\"https://imoveisfederais.planejamento.gov.br/spin-web/login\" --><!--                target=\"_blank\"><span>+ Clique Aqui</span> --><!--       <md-icon class=\"icon-forward-pesq-imoveis\" md-svg-src=\"assets/img/icons/arrow-point-to-right.svg\"></md-icon> --><!--     </md-button> --><!--   </div> --></div>");
$templateCache.put("scripts-integrador/pages/home/view/informesMobile.html","<div style=\"margin-top: 20px\"><div flex-xs=100 flex class=\"home-box infos\"><header class=home-box-header><h2 class=informe-header><b>INFORMES</b></h2></header><div><div layout=row layout-xs=column><div layout=column flex-xs=100 flex=50 flex-sm=100 ng-repeat=\"item in homeCtrl.informesHome | limitTo : 1\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><div ng-if=item.imagem class=informe-caixa-img><img ng-src={{homeCtrl.gerarSrcImagem(item.imagem)}} height=150 width=300></div><div><div class=informe-data>{{item.dataPublicacao | date : \'dd/MM/yyyy\'}}</div><div class=informe-titulo>{{item.titulo}}</div><div class=informe-descricao-mobile>{{homeCtrl.descricaoExtensa(item.corpo)}}<md-button class=btn-continue-lendo ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo</md-button></div></div></div><div layout=column flex=50 flex-xs=100 hide-sm><div layout=column flex-xs=100 flex=30 ng-repeat=\"item in homeCtrl.informesHome | limitTo : 2:1\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><div class=informe-data>{{item.dataPublicacao | date : \'dd/MM/yyyy\'}}</div><div class=informe-titulo>{{item.titulo}}</div><div class=informe-descricao-mobile>{{homeCtrl.descricaoExtensa(item.corpo)}}<md-button class=btn-continue-lendo ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo</md-button></div></div></div></div></div></div></div><div layout=column layout-align=\"end end\"><md-button class=color-infos ng-click=\"homeCtrl.resetInformeSelecionado(); homeCtrl.showInformesDialog($event)\"><span>+ Informes</span><md-icon class=icon-forward-infos md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div>");
$templateCache.put("scripts-integrador/pages/home/view/painelServicos.html","<div flex=100 layout-xs=column style=\"margin-top: 20px; margin-left: 17px\" flex-xs=100><div class=\"home-box painel-servicos\" flex><header class=home-box-header><h2><b>PAINEL DE SERVIÇOS</b></h2></header><div layout-md=row layout-sm=row><div layout=column class=painel-servicos-box flex-md=50 flex-sm=50><div layout=row class=painel-servicos-box-itens><div layout=row flex=25><i class=\"fa fa-files-o fa-3x amarelo\" aria-hidden=true></i></div><div flex=35>Total de Requerimentos</div><div flex=40 class=painel-servicos-box-valores>{{homeCtrl.dadosPainelServico.totalRequerimentos}}</div></div></div><div layout=column class=painel-servicos-box flex-md=50 flex-sm=50><div layout=row class=painel-servicos-box-itens><div layout=row flex=25><i class=\"fa fa-exclamation-triangle fa-3x vermelho\" aria-hidden=true></i></div><div flex=35>Número de Requerimentos Pendentes</div><div flex=40 class=painel-servicos-box-valores>{{homeCtrl.dadosPainelServico.totalPendentes}}</div></div></div></div><div layout-md=row layout-sm=row><div layout=column class=painel-servicos-box flex-md=50 flex-sm=50><div layout=row class=painel-servicos-box-itens><div layout=row flex=25><i class=\"fa fa-tachometer fa-3x azul\" aria-hidden=true></i></div><div flex=35>Percentual de Requerimentos Pendentes</div><div flex=40 class=painel-servicos-box-valores>{{homeCtrl.dadosPainelServico.percentualPendentes}} %</div></div></div><div layout=column class=painel-servicos-box flex-md=50 flex-sm=50><div layout=row class=painel-servicos-box-itens><div layout=row flex=25><i class=\"fa fa-clock-o fa-3x verde\" aria-hidden=true></i></div><div flex=35>Tempo Médio de Atendimento</div><div flex=40 class=painel-servicos-box-valores>{{homeCtrl.dadosPainelServico.prazoMedio}} dias</div></div></div></div></div></div><div layout=column layout-align=\"end end\"><md-button class=color-painel-servicos target=_blank href=http://painel.su.spu.planejamento.gov.br><span>+ Ver Painel</span><md-icon class=icon-forward-painel-servicos md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div>");
$templateCache.put("scripts-integrador/pages/home/view/pendencias.html","<div layout=row layout-xs=column><div layout=column flex=100 class=\"home-box pendencias-box\" layout-xs=column><header class=home-box-header><h2><b>LISTA DE PENDÊNCIAS</b></h2></header><section><div layout=row layout-xs=column layout-align=center class=pendencias-geral><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div ng-style=servicoStyle id=pendenciasServicos class=pendencia-item ng-class=\"homeCtrl.pendenciasServicos.quantidadeTotalPendencias > 0 ? \'pendencia-item-servico\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 1);\" verificar-permissao-pendencia=\"SERVICOS.MANTER_SERVICO, SERVICOS.EXCLUIR_SERVICO, SERVICOS.CONSULTAR_SERVICO,\n                         SERVICOS.CONSULTAR_TRIAGEM,SERVICOS.EFETUAR_ANALISE_TECNICA,SERVICOS.VISUALIZAR_ANALISE_TECNICA,SERVICOS.CONCLUIR_ANALISE\"><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasServicos.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/servicos-icon.png style=\"opacity: 0.3;  width:30%\"></div></div><a href=# class=pendecia-footer>SERVIÇOS</a></div></md-content><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div class=pendencia-item ng-class=\"homeCtrl.pendenciasCadastroImovel.quantidadeTotalPendencias > 0 ? \'pendencia-item-cadastro\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 2);\" verificar-permissao-pendencia=PERMISSAO_CADASTRO><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasCadastroImovel.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/cadastro-icon.png style=\"opacity: 0.2; margin-right: 5px; width:30%\"></div></div><a href=# class=pendecia-footer>CADASTRO</a></div></md-content><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div class=pendencia-item ng-class=\"homeCtrl.pendenciasAvaliacaoContabilidade.quantidadeTotalPendencias > 0 ? \'pendencia-item-avaliacao\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 3);\" verificar-permissao-pendencia=PERMISSAO_AVALIACAO><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasAvaliacaoContabilidade.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/avaliacao-icon.png style=\"opacity: 0.2; margin-right: 5px; width:23%\"></div></div><a href=# class=pendecia-footer>AVALIAÇÃO</a></div></md-content><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div class=pendencia-item ng-class=\"homeCtrl.pendenciasDestinacao.quantidadeTotalPendencias > 0 ? \'pendencia-item-destinacao\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 4);\" verificar-permissao-pendencia=\"DESTINACAO.DESTINACAO_CANCELAR_CESSAO_GRATUITA, DESTINACAO.DESTINACAO_CANCELAR_DOACAO,\n                         DESTINACAO.DESTINACAO_CANCELAR_VENDA, DESTINACAO.DESTINACAO_CANCELAR_POSSE_INFORMAL, DESTINACAO.DESTINACAO_CANCELAR_CUEM,\n                         DESTINACAO.DESTINACAO_CANCELAR_CDRU, DESTINACAO.DESTINACAO_CANCELAR_TERMO_ENTREGA, DESTINACAO.DESTINACAO_CANCELAR_USO_PROPRIO,\n                         DESTINACAO.DESTINACAO_CANCELAR_PERMISSAO_USO_IMOVEL_FUNCIONAL, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_CESSAO_GRATUITA,\n                         DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_DOACAO, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_VENDA, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_POSSE_INFORMAL,\n                         DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_CUEM, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_CDRU, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_TERMO_ENTREGA,\n                         DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_USO_PROPRIO, DESTINACAO.DESTINACAO_DESPACHO_CANCELAR_PERMISSAO_USO_IMOVEL_FUNCIONAL, DESTINACAO.DESTINACAO_ENCERRAR_CESSAO_GRATUITA,\n                         DESTINACAO.DESTINACAO_ENCERRAR_DOACAO, DESTINACAO.DESTINACAO_ENCERRAR_VENDA, DESTINACAO.DESTINACAO_ENCERRAR_POSSE_INFORMAL, DESTINACAO.DESTINACAO_ENCERRAR_CUEM,\n                         DESTINACAO.DESTINACAO_ENCERRAR_CDRU, DESTINACAO.DESTINACAO_ENCERRAR_TERMO_ENTREGA, DESTINACAO.DESTINACAO_ENCERRAR_USO_PROPRIO, DESTINACAO.DESTINACAO_ENCERRAR_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                         DESTINACAO.DESTINACAO_ENCERRAR_CESSAO_ONEROSA, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_CESSAO_GRATUITA, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_DOACAO,\n                         DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_VENDA, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_POSSE_INFORMAL, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_CUEM,\n                         DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_CDRU, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_TERMO_ENTREGA, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_USO_PROPRIO,\n                         DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_PERMISSAO_USO_IMOVEL_FUNCIONAL, DESTINACAO.DESTINACAO_DESPACHO_ENCERRAR_CESSAO_ONEROSA\"><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasDestinacao.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/destinacao-icon.png style=\"opacity: 0.2; margin-right: 5px; width:30%\"></div></div><a href=# class=pendecia-footer>DESTINAÇÃO</a></div></md-content><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div class=pendencia-item ng-class=\"homeCtrl.pendenciasReceitas.quantidadeTotalPendencias > 0 ? \'pendencia-item-receitas\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 5);\" verificar-permissao-pendencia=PERMISSAO_RECEITAS><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasReceitas.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/receitas-icon.png style=\"opacity: 0.3;  width:30%\"></div></div><a href=# class=pendecia-footer>RECEITAS</a></div></md-content><md-content flex class=\"no-scrollbar-y conteudo-pendencias\"><div class=pendencia-item ng-class=\"homeCtrl.pendenciasFiscalizacao.quantidadeTotalPendencias > 0 ? \'pendencia-item-fiscalizacao\' : \'\'\" ng-click=\"homeCtrl.showPendenciasDialog($event, 6);\" verificar-permissao-pendencia=PERMISSAO_FISCALIZACAO><div class=inner><p class=pendencia-item-contador>{{homeCtrl.pendenciasFiscalizacao.quantidadeTotalPendencias | number : 0}}</p><div class=icon><img src=assets/img/icons/fiscalizacao-icon.png style=\"opacity: 0.2;  width:30%\"></div></div><a href=# class=pendecia-footer>FISCALIZAÇÃO</a></div></md-content><!--\n                               <md-content flex class=\"no-scrollbar-y conteudo-pendencias\">\n                                   <div class=\"pendencia-item\" style=\"background-color: #f39c12 !important\" ng-click=\"homeCtrl.showPendenciasDialog($event, 7);\"\n                                   verificar-permissao-pendencia=\"PERMISSAO_GEOINFORMACOES\">\n                                       <div class=\"inner\">\n                                           <p class=\"pendencia-item-contador\">{{homeCtrl.pendenciasGeoInformacoes.quantidadeTotalPendencias  | number : 0}}</p>\n                                           <div class=\"icon\">\n                                               <img src=\"assets/img/icons/geoinformacoes-icon.png\" style=\"opacity: 0.3;  width:30%\"/>\n                                           </div>\n                                       </div>\n                                       <a href=\"#\" class=\"pendecia-footer\">GEOINFORMAÇÕES </a>\n                                   </div>\n                               </md-content>\n               --></div></section></div></div>");
$templateCache.put("scripts-integrador/pages/home/view/pesquisaImovel.html","<div flex=100 layout-xs=column style=\"margin-top: 20px; margin-left: 17px\"><div class=\"home-box pesquisa\"><header class=home-box-header><h2><b>PESQUISA DE IMÓVEL</b></h2></header><div flex=100 class=pesquisa-img-container><img src=/assets/img/mapa_imovel.png class=\"img-thumbnail img-circle img-responsive\"> <a href=https://imoveisfederais.planejamento.gov.br/spin-web/login target=_blank><i class=\"fa fa-search fa-5x\" aria-hidden=true style=\"margin-top: 30%\"></i></a></div><!--<a href=\"https://imoveisfederais.planejamento.gov.br/spin-web/login\" target=\"_blank\" id=\"pesquisaImovel\" >\n            <div style=\"width: 100%; height: 87%; background-repeat: round;\" id=\"mapa-pesquisa-imovel\"\n                 ng-style=\"{\'background-image\':\'url(/assets/img/mapa.png)\' }\">\n\n                <i class=\"fa fa-search fa-5x\" aria-hidden=\"true\"></i>\n            </div>\n        </a>--></div><div layout=column style=\"align-items: flex-end\"><md-button class=color-pesq-imoveis ng-href=https://imoveisfederais.planejamento.gov.br/spin-web/login target=_blank><span>+ Clique Aqui</span><md-icon class=icon-forward-pesq-imoveis md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div></div>");
$templateCache.put("scripts-integrador/pages/informes/language/pt-BR.json","{ \"label-titulo\": \"Título:\", \"label-periodo\": \"Período de publicação\", \"label-data\": \"Data\", \"label-data-publicacao\": \"Data de Publicação\", \"label-texto\": \"Texto\", \"label-acoes\": \"Ações\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-novo\": \"Novo\", \"btn-gravar\": \"Gravar\", \"btn-pre-visualizar\": \"Pré Visualizar\", \"tooltip-limpar-campos\": \"Limpar campos da tela\", \"tooltip-pesquisar\": \"Pesquisar dados conforme filtro\", \"tooltip-novo\": \"Incluir novo registro\", \"label-imagem\": \"Imagem:\", \"label-selecionar-arquivo\": \"Selecionar arquivo\" }");
$templateCache.put("scripts-integrador/pages/informes/view/cadastroInformes.html","<md-content flex><h2 class=tituloFormulario>Cadastro de Informes</h2><md-content flex><form name=informeForm ng-submit=\"validarFormulario(informeForm) && enviarFormulario()\" novalidate><div layout=row layout-xs=column flex=30><label>{{\'label-data-publicacao\' | translate}}: &ensp;</label><label>{{informe.dataPublicacao ? informe.dataPublicacao : dataPublicacaoInforme | date : \'dd/MM/yyyy\'}}</label></div><div layout=row layout-xs=column style=\"margin-top: 1%\"><md-input-container class=md-block flex-gt-xs style=\"margin-bottom: 0px !important\"><label>{{\'label-titulo\' | translate}}</label><input ng-model=informe.titulo name=titulo type=text required><!-- <p ng-show=\"informeForm.titulo.$invalid && !informeForm.titulo.$pristine\" class=\"obrigatorio\">{{\'erro-campos-obrigatorio\' | translate}}</p> --></md-input-container></div><div layout=row layout-xs=column style=\"margin-top: 1%\"><md-input-container class=md-block flex=50 flex-gt-xs style=\"margin-bottom: 0px !important\"><label>{{\'label-imagem\' | translate}}</label><input ng-model=imagem.name name=imagem type=text disabled></md-input-container><md-container flex-gt-sm flex-offset=5><md-button class=md-raised ngf-select=uploadImagem($files) id=file name=file ngf-multiple=false accept=image/*>{{\'label-selecionar-arquivo\' | translate}}</md-button></md-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs style=\"margin-top: 0px !important\"><label>{{\'label-texto\' | translate}}*:</label></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs><text-angular ng-model=informe.corpo name=corpo required ta-toolbar-button-class=\"btn btn-default\" ta-toolbar-button-active-class=active></text-angular><!-- <p ng-show=\"informeForm.corpo.$invalid && !informeForm.corpo.$pristine\" class=\"obrigatorio\">{{\'erro-campos-obrigatorio\' | translate}}</p> --></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=limpar()><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate}}</md-button><md-button class=md-raised type=submit value=PRE_VISUALIZAR ng-click=\"acaoSubmit=PRE_VISUALIZAR\">{{\'btn-pre-visualizar\' | translate}}</md-button><md-button class=md-raised type=submit exibir-elemento-por-permissao=INTEGRADOR.INFORMES_CADASTRAR ng-click=\"acaoSubmit=SALVAR\">{{\'btn-gravar\' | translate}}</md-button></section></div><!--- fim dos botoes --></form></md-content></md-content>");
$templateCache.put("scripts-integrador/pages/informes/view/consultarInformes.html","<md-content flex><h2 class=tituloFormulario>Consulta de Informes</h2><md-content flex><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=70><label>{{\'label-titulo\' | translate}}</label><input ng-model=filtros.titulo type=text></md-input-container><md-input><h4 style=\"margin: 0; font-weight: normal\">{{\'label-periodo\' | translate}}</h4><md-datepicker ng-model=filtros.dataPublicacaoInicio md-open-on-focus></md-datepicker></md-input><md-input class=md-block style=\"padding-top: 11px; padding-left: 2px\"><label>a</label><md-datepicker ng-model=filtros.dataPublicacaoFim></md-datepicker></md-input></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=clearFilter()><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate}}</md-button><md-button class=md-raised exibir-elemento-por-permissao=INTEGRADOR.INFORMES_CONSULTAR ng-click=findInformesByFilter()><md-tooltip md-direction=bottom>{{\'tooltip-pesquisar\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate}}</md-button><md-button class=md-raised exibir-elemento-por-permissao=INTEGRADOR.INFORMES_CADASTRAR ng-click=\"redirecionaTela(\'cadastroInformes\')\"><md-tooltip md-direction=bottom>{{\'tooltip-novo\' | translate}}</md-tooltip>{{\'btn-novo\' | translate}}</md-button></section></div><!--- fim dos botoes --><div flex class=caixa-tabela-resultado><div layout=column ng-show=\"listaInformes.length > 0\"><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-data\' | translate}}</th><th md-column>{{\'label-titulo\' | translate}}</th><th md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaInformes\"><td md-cell><span ng-bind=item.dataPublicacao></span></td><td md-cell><span ng-bind=item.titulo></span></td><td md-cell><span layout=row layout-align=\"left center\"><a style=\"text-decoration: none\" ng-click=carregaInformeEdicao(item)><ng-md-icon exibir-elemento-por-permissao=INTEGRADOR.INFORMES_CADASTRAR class=hand icon=create style=fill:#788489 size=22></ng-md-icon></a><ng-md-icon class=hand icon=clear style=fill:#788489 size=22 exibir-elemento-por-permissao=INTEGRADOR.INFORMES.EXCLUIR ng-click=\"showConfirmExcluir($event, item.id, $index);\"><md-tooltip>Excluir Informe</md-tooltip></ng-md-icon></span></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listaInformes.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findAll md-page-select></md-table-pagination></md-conent></div></div></md-content></md-content>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/language/pt-BR.json","{ \"label-modulo\": \"Módulo\", \"label-instrumento-funcionalidade\": \"Instrumento/Funcionalidade\", \"label-competencia-autorizacao\": \"Competência para autorização\", \"label-fundamento-legal\": \"Fundamento Legal\", \"label-remover-fundamento-legal\": \"Remover Fundamento Legal\", \"label-confirmacao-remover-fundamento\":\"Este fundamento legal está vinculado a itens no checklist. Se for removido, os itens permanecerão no checklist desvinculados do fundamento. Deseja continuar?\", \"label-checklist-especifico\": \"Checklist Específico de Análise\", \"label-item-checklist-especifico\": \"Item do Checklist Específico de Análise\", \"label-adicionar\": \"Adicionar\", \"label-retornar\": \"Retornar\", \"label-gravar\": \"Gravar\", \"label-consultar\": \"Consultar\", \"label-inserir\": \"inserir\", \"label-acoes\": \"Ações\", \"label-confirmar\": \"Confirmar\", \"label-item\": \"Item\", \"label-justificativa\": \"Justificativa\", \"label-suspensao\": \"SUSPENÇÃO\", \"label-confirmar-edicao\":\"Confirmar Edição\", \"label-cancelar-edicao\":\"Cancelar Edição\", \"label-modo-consulta\":\"Modo Consulta\", \"label-modo-inclusao\":\"Modo Inclusão\", \"label-consulta-fundamento-legal\":\"Consultar Fundamento Legal\", \"label-consulta-checklist-especifico\":\"Consultar Checklist\", \"label-competencia\":\"Competência\", \"label-exibir-ato\":\"Exibir Ato Autorizativo\", \"label-titulo\": \"Título\", \"label-descricao\": \"Descrição\", \"label-responsavel\": \"Responsável\", \"tooltip-editar\": \"Editar\", \"tooltip-pesquisar\": \"Pesquisar\", \"tooltip-novo\": \"Novo\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-novo\": \"Novo\", \"btn-editar\": \"Editar\", \"btn-fundamento-legal\": \"Vincular fundamento legal\", \"btn-check-list\": \"Vincular check list\", \"btn-contrato\": \"Contrato\", \"btn-suspender\":\"Suspender Instrumento/Funcionalidade\", \"btn-confirmar\":\"Confirmar\", \"btn-cancelar\":\"Cancelar\", \"btn-vincular-ato-contrato\":\"Vincular Ato/Contrato\", \"btn-vincular-checklist-de-analise\":\"Vincular Checklist de análise\", \"btn-vincular-fundamento-legal\":\"Vincular Fundamento Legal\", \"msg-nenhum-registro-encontrado\": \"Nenhum registro encontrado\", \"msg-justificativa\": \"Campo Justificativa Obrigatório\", \"msg-instrumento-funcionalidade-suspendida\":\"Instrumento/Funcionalidade suspenso\", \"msg-instrumento-funcionalidade-n-suspensa\": \"Suspensão cancelada\" }");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/cadastroInstrumentoFuncionalidade.html","<div><form name=form><titulo prefixo-titulo=\"\'Cadastrar\'\"></titulo><div layout=row><md-input-container flex=50><label>{{\'label-modulo\' | translate}}:</label><md-select ng-model=instrumento.modulo ng-required=true ng-disabled=isEdicao ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=buscarFuncionalidadePorModulo() name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"modulo in modulos | orderBy:\'descricao\'\" ng-value=modulo>{{modulo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>{{\'label-instrumento-funcionalidade\' | translate}}:</label><md-select ng-model=instrumento.instrumentoFuncionalidade ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-instrumento-funcionalidade\' | translate}}\" ng-disabled=isEdicao><md-option ng-repeat=\"funcionalidade in funcionalidades | orderBy:\'descricao\'\" ng-value=funcionalidade>{{funcionalidade.descricao}}</md-option></md-select></md-input-container><!--<md-input-container flex=\"50\" ng-if=\"idEdicao\">\n        <label>{{\'label-instrumento-funcionalidade\' | translate}}:</label>\n        <input ng-model=\"instrumento.descricao\" disabled>\n      </md-input-container>--></div><div layout=row><md-input-container flex=100><label>{{\'label-titulo\' | translate}}</label><input name=\"{{\'label-titulo\' | translate}}\" ng-model=instrumento.titulo md-maxlength=100 maxlength=100 required></md-input-container></div><div layout=row><md-input-container flex=100 style=\"margin-top: 0\"><label>{{\'label-descricao\' | translate}}</label><input name=\"{{\'label-descricao\' | translate}}\" ng-model=instrumento.descricao md-maxlength=100 maxlength=100 required></md-input-container></div><div style=\"margin-top: 15%\"><div class=margin-top layout=row layout-align=\"end center\"><md-button class=md-raised ng-click=retornar()>{{\'label-retornar\' | translate}}</md-button><md-button class=md-raised ng-show=isEdicao ng-click=vincularFundamentoLegal()>{{\'btn-vincular-fundamento-legal\' | translate}}</md-button><md-button class=md-raised ng-show=isEdicao ng-click=\"\">{{\'btn-vincular-ato-contrato\' | translate}}</md-button><md-button class=md-raised ng-show=isEdicao ng-click=\"\">{{\'btn-vincular-checklist-de-analise\' | translate}}</md-button><md-button class=md-raised ng-click=salvarInstrumento()>{{\'label-gravar\' | translate}}</md-button></div></div></form></div>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/consultaInstrumentoFuncionalidade.html","<div><titulo prefixo-titulo=\"\'Consultar\'\"></titulo><div layout=row><md-input-container flex=50><label>{{\'label-modulo\' | translate}}</label><md-select ng-model=modulo ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=buscarFuncionalidadePorModulo() name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"tipo in modulos | orderBy:\'descricao\'\" ng-value={{tipo}}>{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>{{\'label-instrumento-funcionalidade\' | translate}}:</label><md-select ng-model=instrumentoFuncionalidadeId ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-funcionalidade\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"funcionalidade in funcionalidades | orderBy:\'descricao\'\" ng-value={{funcionalidade}}>{{funcionalidade.descricao}}</md-option></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=limparPesquisa() exibir-elemento-por-permissao=\"DESTINACAO_CONSULTAR_INSTRUM_FUNC_C_DOMINIALIDADE,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_GESTAO_PRAIAS,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CESSAO_GRATUITA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CESSAO_ONEROSA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_DOACAO_TRANSF,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CUEM,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_OCUPACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_VENDA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CDRU,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_TR_TITULARIDADE,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_INSC_OCUPACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_AFORAMENTO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_LOCACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_ARRENDAMENTO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_UTILIZACAO_A_REGULARIZAR,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_USO_PROPRIO\"><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate }}</md-button><md-button class=md-raised ng-click=filterAll(instrumentoFuncionalidadeId.id) exibir-elemento-por-permissao=\"DESTINACAO_CONSULTAR_INSTRUM_FUNC_C_DOMINIALIDADE,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_GESTAO_PRAIAS,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CESSAO_GRATUITA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CESSAO_ONEROSA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_DOACAO_TRANSF,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CUEM,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_OCUPACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_VENDA,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_CDRU,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_TR_TITULARIDADE,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_INSC_OCUPACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_AFORAMENTO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_LOCACAO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_ARRENDAMENTO,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_UTILIZACAO_A_REGULARIZAR,\n                                                DESTINACAO_CONSULTAR_INSTRUM_FUNC_USO_PROPRIO\"><md-tooltip md-direction=bottom>{{\'tooltip-pesquisar\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate }}</md-button><md-button class=md-raised ng-click=cadastrarNovoInstrumento() exibir-elemento-por-permissao=\"DESTINACAO_MANTER_INSTRUM_FUNC_C_DOMINIALIDADE,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_GESTAO_PRAIAS,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_CESSAO_GRATUITA,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_CESSAO_ONEROSA,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_DOACAO_TRANSF,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_CUEM,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_OCUPACAO,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_VENDA,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_CDRU,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_TR_TITULARIDADE,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_INSC_OCUPACAO,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_AFORAMENTO,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_LOCACAO,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_ARRENDAMENTO,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_UTILIZACAO_A_REGULARIZAR,\n                                                DESTINACAO_MANTER_INSTRUM_FUNC_USO_PROPRIO\"><md-tooltip md-direction=bottom>{{\'tooltip-novo\' | translate}}</md-tooltip>{{\'btn-novo\' | translate }}</md-button></section></div><!--- fim dos botoes --><div flex style=\"margin-top: 3%\" class=switch ng-show=\"funcionalidadesList.length > 0\"><div layout=column><md-conent flex><md-table-container><table md-table style=\"border: 1px solid #cccccc\"><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\" class=upload-head><th md-column class=texto-centro>Módulo</th><th md-column class=texto-centro>Instrumento / Funcionalidade</th><th md-column style=\"padding-left: 90px\">Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"funcionalidade in funcionalidadesList | orderBy: funcionalidade.modulo.descricao\"><td md-cell class=texto-centro-corpo><span ng-bind=funcionalidade.instrumentoFuncionalidade.modulo.descricao></span></td><td md-cell class=texto-centro-corpo><span ng-bind=funcionalidade.instrumentoFuncionalidade.descricao></span></td><td md-cell class=texto-centro-corpo style=\"position: relative\"><md-fab-speed-dial md-direction=left class=\"hand md-scale\" style=\"position: relative\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><div exibir-elemento-por-permissao=\"DESTINACAO_MANTER_INSTRUM_FUNC_C_DOMINIALIDADE,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_GESTAO_PRAIAS,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_CESSAO_GRATUITA,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_CESSAO_ONEROSA,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_DOACAO_TRANSF,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_CUEM,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_OCUPACAO,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_VENDA,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_CDRU,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_TR_TITULARIDADE,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_INSC_OCUPACAO,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_AFORAMENTO,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_LOCACAO,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_ARRENDAMENTO,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_UTILIZACAO_A_REGULARIZAR,\n                                                      DESTINACAO_MANTER_INSTRUM_FUNC_USO_PROPRIO\"><ng-md-icon class=icon-subacao icon=edit ng-click=editarFuncionalidade(funcionalidade)><md-tooltip>{{\"btn-editar\" | translate}}</md-tooltip></ng-md-icon></div><ng-md-icon class=icon-subacao ng-click=vincularFundamentoLegal(funcionalidade) icon=gavel><md-tooltip>{{\"btn-fundamento-legal\" | translate}}</md-tooltip></ng-md-icon><ng-md-icon class=icon-subacao icon=playlist_add_check><md-tooltip>{{\"btn-check-list\" | translate}}</md-tooltip></ng-md-icon><div class=icone-contrato><md-tooltip>{{\"btn-contrato\" | translate}}</md-tooltip><img src=../../../../assets/img/contrato_grey.png></div><div exibir-elemento-por-permissao=\"DESTINACAO_SUSPENDER_INSTRUM_FUNC_C_DOMINIALIDADE,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_GESTAO_PRAIAS,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_CESSAO_GRATUITA,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_CESSAO_ONEROSA,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_DOACAO_TRANSF,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_CUEM,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_OCUPACAO,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_VENDA,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_CDRU,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_TR_TITULARIDADE,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_INSC_OCUPACAO,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_AFORAMENTO,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_LOCACAO,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_ARRENDAMENTO,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_PERMISSAO_USO_IMOVEL_FUNCIONAL,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_UTILIZACAO_A_REGULARIZAR,\n                                                    DESTINACAO_SUSPENDER_INSTRUM_FUNC_USO_PROPRIO\"><md-tooltip>{{\"btn-suspender\" | translate}}</md-tooltip><md-switch ng-model=funcionalidade.suspenso ng-change=suspenderInstrumento(funcionalidade) exibir-elemento-por-permissao=INTEGRADOR.INSTR-FUNC.SUSPENDER class=\"md-primary switch\" style=\"margin: -4px 0px 0px 0px\"></md-switch></div></md-fab-actions></md-fab-speed-dial></td></tr></tbody></table></md-table-container><md-table-pagination style=\"border: 1px solid #cccccc; margin:auto; height: 63px\" ng-show=\"funcionalidadesList.length > 0\" md-limit=paginacao.limit md-limit-options=paginacao.limitsPage md-page=paginacao.page md-total={{totalItems}} md-on-paginate=filterAll md-page-select></md-table-pagination></md-conent></div></div></div>");
$templateCache.put("scripts-integrador/pages/orientacao-tecnica/language/pt-BR.json","{ \"label-selecionar-arquivo\": \"Selecionar arquivo\", \"button-editar\": \"Editar\", \"button-deletar\": \"Deletar\", \"button-visualizar\": \"Visualizar\" }");
$templateCache.put("scripts-integrador/pages/orientacao-tecnica/view/orientacaoTecnica.html","<h1>Cadastro de Orientação Técnica</h1><form name=orientacaoTecnicaForm novalidate layout=column ng-submit=salvar() class=custom-style><md-input-container><label>Nome da Orientação Técnica:</label><input required ng-model=orientacaoTecnica.titulo maxlength=100 md-maxlength=100 ng-disabled=consultar></md-input-container><div layout=row flex=100><md-input-container flex=50><label>Área:</label><md-select ng-model=orientacaoTecnica.areaOrientacaoTecnica required ng-disabled=consultar><md-option ng-repeat=\"area in areasOrientacaoTecnica\" ng-selected=\"area.id == orientacaoTecnica.areaOrientacaoTecnica.id\" ng-value=area>{{area.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>Assunto:</label><md-select ng-model=orientacaoTecnica.assuntoOrientacaoTecnica ng-disabled=consultar><md-option ng-repeat=\"assunto in assuntosOrientacaoTecnica\" ng-selected=\"assunto.id == orientacaoTecnica.assuntoOrientacaoTecnica.id\" ng-value=assunto>{{assunto.descricao}}</md-option></md-select></md-input-container></div><div><h3 class=green-text>Links de Referência</h3><md-table-container class=\"md-block margin-top-2-em\" flex-gt-xs flex=100><table md-table><thead md-head><tr md-row><th md-column>Tipo do Ato/Orientação Técnica</th><th md-column>Nº do Ato/Orientação Técnica</th><th md-column>Link</th><th md-column style=\"text-align: center !important\">Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"link in orientacaoTecnica.listLinkOrientacaoTecnica track by $index\" ng-if=!link.isExcluido><td md-cell>{{link.tipoOrientacaoTecnica.descricao}}</td><td md-cell>{{link.numeroOrientacaoTecnica}}</td><td md-cell>{{link.descricaoLink}}</td><td md-cell style=\"position: relative; display: block\"><md-fab-speed-dial md-direction=left style=\"margin-right: 50%; margin-top: -3px; transform: translateX(20%)\" class=\"hand md-scale alinhar-md-fab-speed-dial\" layout-align=\"end end\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><a href=\"\" ng-click=abrirModalLink($index) ng-if=!consultar><ng-md-icon class=icon-subacao icon=create style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-editar\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=deletarLink($index) ng-if=!consultar><ng-md-icon class=icon-subacao icon=delete style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-deletar\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=\"abrirModalLink($index, true)\"><ng-md-icon class=icon-subacao icon=search style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-visualizar\' | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></td></tr><tr ng-if=\"!orientacaoTecnica.listLinkOrientacaoTecnica || orientacaoTecnica.listLinkOrientacaoTecnica.length == 0\"><td>Nenhum item...</td></tr></tbody></table></md-table-container><div layout=row layout-align=end><md-button class=md-raised ng-click=abrirModalLink() ng-if=!consultar>Adicionar Link</md-button></div></div><div><h3 class=green-text>Procedimentos Operacionais Padrão</h3><md-table-container class=\"md-block margin-top-2-em\" flex-gt-xs flex=100><table md-table><thead md-head><tr md-row><th md-column>Título</th><th md-column>Finalidade</th><th md-column>Procedimento</th><th md-column style=\"text-align: center !important\">Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"pop in orientacaoTecnica.listProcedimentoOperacionalPadrao track by $index\" ng-if=!pop.isExcluido><td md-cell>{{pop.titulo}}</td><td md-cell>{{pop.finalidade}}</td><td md-cell class=mais-menos-wrapper><input type=checkbox id={{$index}}><div ng-bind-html=pop.procedimento></div><label for={{$index}}></label></td><td md-cell style=\"position: relative; display: block\"><md-fab-speed-dial md-direction=left style=\"margin-right: 50%; margin-top: -3px; transform: translateX(20%)\" class=\"hand md-scale alinhar-md-fab-speed-dial\" layout-align=\"end end\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><a href=\"\" ng-click=editarPop($index) ng-if=!consultar><ng-md-icon class=icon-subacao icon=create style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-editar\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=deletarPop($index) ng-if=!consultar><ng-md-icon class=icon-subacao icon=delete style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-deletar\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=visualizarPop($index)><ng-md-icon class=icon-subacao icon=search style=fill:#788489 size=25 alt=\"\"><md-tooltip>{{\'button-visualizar\' | translate}}</md-tooltip></ng-md-icon></a></md-fab-actions></md-fab-speed-dial></td></tr><tr ng-if=\"!orientacaoTecnica.listProcedimentoOperacionalPadrao || orientacaoTecnica.listProcedimentoOperacionalPadrao.length == 0\"><td>Nenhum item...</td></tr></tbody></table></md-table-container><div layout=row layout-align=end><md-button class=md-raised ng-click=inserirPop() ng-if=!consultar>Adicionar POP</md-button></div></div><md-input-container><label>Resumo da publicação/alteração:</label><textarea ng-model=orientacaoTecnica.resumoPublicacao maxlength=600 md-maxlength=600 ng-disabled=consultar>\n    </textarea></md-input-container><div layout=row layout-align=end><md-button class=md-raised ng-click=voltar()>Voltar</md-button><input class=\"md-raised md-button md-ink-ripple\" type=submit ng-click=setRascunho() value=\"Salvar como rascunho\" ng-if=!consultar> <input class=\"md-raised md-button md-ink-ripple\" type=submit value=Concluir ng-if=!consultar></div></form>");
$templateCache.put("scripts-integrador/pages/procedimento-operacional-padrao/language/pt-BR.json","{ \"label-selecionar-arquivo\": \"Selecionar arquivo\" }");
$templateCache.put("scripts-integrador/pages/procedimento-operacional-padrao/view/procedimentoOperacionalPadrao.html","<h1 class=green-text>Cadastro de Procedimento Operacional Padrão (POP)</h1><form name=formularioPop ng-submit=salvar() novalidate layout=column class=custom-style><md-input-container flex=100><label>Título</label><input ng-model=pop.titulo required maxlength=100 md-maxlength=100 ng-disabled=consultar></md-input-container><md-input-container flex=100><label>Finalidade</label><input ng-model=pop.finalidade required maxlength=200 md-maxlength=200 ng-disabled=consultar></md-input-container><label>Procedimento*</label><md-input-container flex=100 class=textangular><div text-angular ng-model=pop.procedimento name=descricao ta-max-text=5000 ng-if=!consultar ta-toolbar-button-class=\"btn btn-default\" ta-toolbar-button-active-class=active name=procedimento></div><div class=consultarTextAngular ng-if=consultar ng-bind-html=pop.procedimento></div></md-input-container><h3 class=green-text>Fluxo</h3><md-container flex-gt-sm><ul><li ng-repeat=\"arquivo in pop.listArquivoFluxo\">{{arquivo.nome}}</li></ul><md-button class=md-raised ngf-select=uploadArquivoFluxo($files) id=file name=file ngf-multiple=false ng-disabled=consultar>{{\'label-selecionar-arquivo\' | translate}}</md-button></md-container><h3 class=green-text>Arquivos auxiliares</h3><md-container flex-gt-sm><ul><li ng-repeat=\"arquivo in pop.listArquivoAuxiliar\">{{arquivo.nome}}</li></ul><md-button class=md-raised ngf-select=uploadArquivoAuxiliar($files) id=file2 name=file2 ngf-multiple=false ng-disabled=consultar>{{\'label-selecionar-arquivo\' | translate}}</md-button></md-container><div layout=row layout-align=end><md-button class=md-raised ng-click=voltar()>Voltar</md-button><input class=\"md-raised md-button md-ink-ripple\" type=submit ng-click=setRascunho() ng-disabled=!pop.titulo value=\"Salvar como rascunho\" ng-if=!consultar> <input class=\"md-raised md-button md-ink-ripple\" type=submit value=Concluir ng-if=!consultar></div></form>");
$templateCache.put("scripts-integrador/pages/siapa/view/siapa.tmpl.html","<div ng-if=apresentarFrame()><iframe iframe-set-dimensions-onload src={{url}}></iframe></div>");
$templateCache.put("scripts-integrador/pages/textos-ajuda/language/pt-BR.json","{ \"label-modulo\": \"Módulo\", \"label-funcionalidade-componente\": \"Funcionalidade/Componente\", \"label-data-ultima-atualizacao\": \"Data da Última Atualização\", \"label-campo-formulario\": \"Campo do Formulário\", \"label-texto-ajuda\": \"Texto de Ajuda\", \"btn-salvar\": \"Salvar\" }");
$templateCache.put("scripts-integrador/pages/textos-ajuda/view/cadastroTextosAjuda.html","<md-content flex><h2 ng-show=!textosAjudaCtrl.isEditar class=tituloFormulario>Cadastro Textos de Ajuda</h2><h2 ng-show=textosAjudaCtrl.isEditar class=tituloFormulario>Editar Textos de Ajuda</h2><ng-form name=cadastroTextosAjudaForm novalidate><div layout=row layout-xs=column><md-input-container flex=50><label>{{\'label-modulo\' | translate}}</label><md-select ng-disabled=textosAjudaCtrl.isEditar required ng-model=textosAjudaCtrl.query.modulo ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=textosAjudaCtrl.buscarFuncionalidadePorModuloSemAjuda() name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"modulo in textosAjudaCtrl.modulos | orderBy:\'descricao\'\" ng-value={{modulo}}>{{modulo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>{{\'label-funcionalidade-componente\' | translate}}</label><md-select ng-disabled=textosAjudaCtrl.isEditar required ng-model=textosAjudaCtrl.query.funcionalidade ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-funcionalidade-componente\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"funcionalidade in textosAjudaCtrl.funcionalidades | orderBy:\'id\'\" ng-value={{funcionalidade}}>{{funcionalidade.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column layout-align=end ng-show=!textosAjudaCtrl.isEditar><md-button ng-click=textosAjudaCtrl.pesquisarCamposAjuda() class=md-raised>{{\'btn-pesquisar\' | translate}}</md-button></div><div ng-show=\"textosAjudaCtrl.camposAjuda.length > 0\"><div layout=row layout-xs=column><h2 flex=50 class=titulo-secundario-formulario>{{\'label-campo-formulario\' | translate}}</h2><h2 flex=50 class=titulo-secundario-formulario>{{\'label-texto-ajuda\' | translate}}</h2></div><div ng-repeat=\"campoAjuda in textosAjudaCtrl.camposAjuda | orderBy:\'id\'\"><div layout=row layout-xs=column><md-input-container flex=50><span ng-bind=campoAjuda.nome></span></md-input-container><md-input-container flex=50><textarea ng-model=campoAjuda.ajuda name=textoAjuda ng-maxlength=500 maxlength=500></textarea><span class=textAreaCount>{{campoAjuda.ajuda.length}} / 500</span></md-input-container></div><div ng-repeat=\"subCampoAjuda in campoAjuda.nos\"><div layout=row layout-xs=column><md-input-container flex=50><span class=sub-campo-ajuda ng-bind=subCampoAjuda.nome></span></md-input-container><md-input-container flex=50><textarea ng-model=subCampoAjuda.ajuda name=textoAjuda ng-maxlength=500 maxlength=500></textarea><span class=textAreaCount>{{subCampoAjuda.ajuda.length}} / 500</span></md-input-container></div></div><div class=separador></div></div></div><div layout=row layout-xs=column layout-align=\"end right\"><md-button ng-click=\"textosAjudaCtrl.redirecionarTela(\'INTEGRADOR.TEXTOS_AJUDA\')\" class=md-raised>{{\'btn-fechar\' | translate}}</md-button><md-button ng-show=\"textosAjudaCtrl.camposAjuda.length > 0\" ng-click=textosAjudaCtrl.salvarTextosAjuda(cadastroTextosAjudaForm) class=md-raised>{{\'btn-salvar\' | translate}}</md-button></div></ng-form></md-content>");
$templateCache.put("scripts-integrador/pages/textos-ajuda/view/manutencaoTextosAjuda.html","<md-content flex><h2 class=tituloFormulario>Manutenção de Textos de Ajuda</h2><md-content flex><div layout=row layout-xs=column><md-input-container flex=50><label>{{\'label-modulo\' | translate}}</label><md-select ng-model=textosAjudaCtrl.query.modulo ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=textosAjudaCtrl.buscarFuncionalidadePorModulo() name=\"{{\'label-modulo\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"modulo in textosAjudaCtrl.modulos | orderBy:\'descricao\'\" ng-value={{modulo}}>{{modulo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>{{\'label-funcionalidade-componente\' | translate}}</label><md-select ng-model=textosAjudaCtrl.query.funcionalidade ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-funcionalidade-componente\' | translate}}\"><md-option value={{null}}>{{\'selecione-combo\' | translate}}</md-option><md-option ng-repeat=\"funcionalidade in textosAjudaCtrl.funcionalidades | orderBy:\'id\'\" ng-value={{funcionalidade}}>{{funcionalidade.nome}}</md-option></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=textosAjudaCtrl.limpar()><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate}}</md-button><md-button class=md-raised ng-click=textosAjudaCtrl.pesquisar()><md-tooltip md-direction=bottom>{{\'tooltip-pesquisar\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate}}</md-button><md-button class=md-raised ng-click=\"textosAjudaCtrl.redirecionarTela(\'INTEGRADOR.CADASTRO_TEXTOS_AJUDA\')\">{{\'btn-novo\' | translate}}</md-button></section></div><!--- fim dos botoes --><div flex><div layout=column ng-show=\"textosAjudaCtrl.listaFuncionalidades.length > 0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-modulo\' | translate}}</th><th md-column>{{\'label-funcionalidade-componente\' | translate}}</th><th md-column>{{\'label-data-ultima-atualizacao\' | translate}}</th><th md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in textosAjudaCtrl.listaFuncionalidades\"><td md-cell><span ng-bind=item.modulo.descricao></span></td><td md-cell><span ng-bind=item.nome></span></td><td md-cell><span ng-bind=\"item.dataUltimaAtualizacao | date : \'dd/MM/yyyy\':\'UTC\'\"></span></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=textosAjudaCtrl.irParaEdicao(item.id) class=\"md-fab md-mini\" aria-label=\"Editar Campo de Ajuda\" style=\"background-color: #f7f7f7\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Campo de Ajuda</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"textosAjudaCtrl.listaFuncionalidades.length > 0\" md-limit=textosAjudaCtrl.query.maximoPorPagina md-limit-options=\"[5, 10, 15]\" md-page=textosAjudaCtrl.query.numeroPagina md-total={{textosAjudaCtrl.query.totalElements}} md-on-paginate=textosAjudaCtrl.pesquisar md-page-select></md-table-pagination></md-content></div></div></md-content></md-content>");
$templateCache.put("scripts-integrador/app/directives/fundamento-legal/template/incluirUtilizacao.html","<div layout=row flex><h3>{{\'label-utilizacao\' | translate}}</h3></div><div layout=row flex-offset=0 class=margin-top layout-wrap><md-input-container style=\"margin-top: 0px\" flex=40 class=md-block><label>{{\'label-tipo-uso-permitido\'| translate}}</label><md-select multiple md-on-open=buscarEspecificacoes() ng-change=buscarEspecificacao(); ng-model=utilizacao.tipoUsoPermitido ng-disabled=\"bloquear || isDetalhar\"><md-checkbox class=md-primary ng-model=todosTipos ng-if=\"listaTipoUsoPermitido.length > 0\" ng-change=selecionarTodosTipo(todosTipos)>{{\'label-todos\' | translate}}</md-checkbox><md-option ng-repeat=\"item in listaTipoUsoPermitido\" ng-value=item ng-if=validarExibicaoTipoPermitido(item)>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container style=\"margin-top: 0px\" flex=40 class=md-block><label>{{\'label-especificacao\'| translate}}</label><md-select multiple ng-disabled=\"bloquear || isDetalhar\" ng-model=utilizacao.especificacoes><md-checkbox class=md-primary ng-if=\"listaEspecificacao.length > 0\" ng-model=tipoEspecificacao ng-change=selecionarTodosEspecificacao(tipoEspecificacao)>{{\'label-todos\' | translate}}</md-checkbox><md-option ng-repeat=\"itemEspecificacao in listaEspecificacao \" ng-value=itemEspecificacao ng-if=validarExibicaoEspecificacao(itemEspecificacao)>{{itemEspecificacao.descricao}}</md-option></md-select></md-input-container><md-button class=md-raised ng-disabled=\"bloquear || isDetalhar\" flex=20 ng-click=adicionarListaUtilizacoes()>{{\'label-adicionar\'| translate}}</md-button></div><md-content ng-show=\"utilizacoes.length > 0\"><md-table-container><table md-table class=md-border-table><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-utilizacao\'|translate}}</th><th md-column>{{\'label-especificacao\' | translate}}</th><th md-column ng-show=\"!bloquear && !isDetalhar\">{{\'label-acao\'|translate}}</th></tr></thead><tbody md-body><tr ng-repeat-start=\"itemUsoEspecificacao in utilizacoes\"><td rowspan={{itemUsoEspecificacao.especificacoes.length}}><label>{{itemUsoEspecificacao.tipoUsoPermitido.descricao}}</label></td><td>{{itemUsoEspecificacao.especificacoes[0].descricao}}</td><td ng-show=\"!bloquear && !isDetalhar\"><ng-md-icon class=icon-subacao icon=delete ng-click=\"removerUsoEspecificacao($event, 0, itemUsoEspecificacao)\"><md-tooltip>{{\"label-excluir\" | translate}}</md-tooltip></ng-md-icon></td></tr><tr ng-repeat=\"esp in itemUsoEspecificacao.especificacoes\" ng-if=!$first ng-repeat-end><td>{{esp.descricao}}</td><td ng-show=\"!bloquear && !isDetalhar\"><ng-md-icon class=icon-subacao icon=delete ng-click=\"removerUsoEspecificacao($event, $index, itemUsoEspecificacao)\"><md-tooltip>{{\"label-excluir\" | translate}}</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><div class=paginationUtilizacao></div></md-content>");
$templateCache.put("scripts-integrador/app/directives/secoes/templates/adicionarClausulas.html","<md-content flex><div layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised style=\"margin-left: 0\" ng-click=abrir($event)>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-if=\"clausulas.length > 0\" class=top-1><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-clausulas\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr ng-repeat=\"item in clausulas | orderBy:\'ordem\'\" md-row md-select=item md-select-id=id md-auto-select><td md-cell class=texto-centro-corpo>{{item.descricao}}</td><td md-cell class=texto-centro-corpo><a href=\"\" ng-click=abrir($event) style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22><md-tooltip>{{labelEditar}}</md-tooltip></ng-md-icon></a><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"Excluir documento\" ng-click=\"remover($event, $index);\"><md-tooltip>{{labelExcluir}}</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><!-- <md-table-pagination ng-show=\"listaDocumento.length > 0\"\n                                 md-limit=\"queryDocumento.limit\" md-limit-options=\"queryDocumento.limitsPage\"\n                                 md-page=\"queryDocumento.page\" md-total=\"{{listaDocumento.length}}\"\n                                 md-page-select=\"true\">\n            </md-table-pagination> --></div></div></md-content>");
$templateCache.put("scripts-integrador/app/directives/secoes/templates/adicionarEditorHtml.html","<md-content flex><div layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=abrir() style=\"margin-left: 0\" ng-if=!model.descricao>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-if=model class=top-1><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{config.titulo}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select><td md-cell class=texto-centro-corpo><label ng-bind-html=model.descricao></label></td><td md-cell class=texto-centro-corpo><a href=\"\" ng-click=editar($event) style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22><md-tooltip>{{labelEditar}}</md-tooltip></ng-md-icon></a><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'excluir-preambulo\' | translate}}\" ng-click=remover($event);><md-tooltip>{{labelExcluir}}</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container></div></div></md-content>");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/partials/modalConfigurarComponentes.html","<md-dialog id=su-integrador flex=60 ng-cloak aria-label=\"{{\'label-checklist-configurar-componetes\' | translate | uppercase}}\"><form name=formularioComponetes novalidate><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-checklist-configurar-componetes\' | translate | uppercase}}</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro></th><th md-column class=texto-centro>{{\'label-informacoes-compor-modelo\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"componentes in dictConfigurarComponente\"><td md-cell class=\"celula tamanho-setas\" style=\"border-top: 0% !important; height: 0% !important\"><div layout=column flex=10><i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index-1, dictConfigurarComponente)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index+1, dictConfigurarComponente)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div ng-if=\"item.hierarquia == \'PRIMARIO\'\" ng-repeat=\"item in componentes.item\"><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox><input ng-model=item.descricao ng-maxlength=40 maxlength=40 class=pai capitalize> <span ng-show=\"mostrarVerCampos(componentes.item) && !componentes.verCampos\" class=\"margem-verCampos hand\" ng-click=\"componentes.verCampos = !componentes.verCampos\">{{\'ver-campos\' | translate}} </span><span ng-show=\"mostrarVerCampos(componentes.item) && componentes.verCampos\" class=\"margem-verCampos hand\" ng-click=\"componentes.verCampos = !componentes.verCampos\">{{\'ocultar-campos\' | translate}}</span></div><div ng-show=componentes.verCampos class=alinhar-checkbox ng-if=\"item.hierarquia == \'SECUNDARIO\'\" ng-repeat=\"item in componentes.item\"><md-checkbox ng-checked=item.status class=md-primary ng-click=\"item.status = !item.status\"></md-checkbox><input ng-model=item.descricao ng-maxlength=40 maxlength=40 class=filho capitalize></div></td></tr></tbody></table></md-table-container></md-content></div><div layout=row layout-sm=column layout-align=\"end center\" class=top-2><md-button class=md-raised ng-click=fechar()>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised ng-click=confirmar()>{{\'btn-confirmar\' | translate}}</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/atos-contratos/view/partials/modalExclusaoModelo.html","<md-dialog id=su-integrador aria-label=Suspender flex=60 ng-cloak><form name=excluirModelo><md-toolbar><div class=\"md-toolbar-tools text-center\"><h2>{{REMOVER}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row flex=100 layout-margin><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}*</label><textarea ng-model=justificativaExclusao required name=justificativa maxlength=500 md-maxlength=500></textarea></md-input-container></div><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=confirmar() class=md-raised>Salvar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/partial/cancelarFundamentoLegal.html","<div style=\"margin: 20px\"><form name=formCancelar><h2 class=tituloFormulario>{{\'label-cancelar-fundamento-legal\' | translate}}</h2><div layout=row><div layout=column flex=50><label>{{\'label-responsavel\' | translate}}:</label><b><p>{{responsavel.nome}} / {{responsavel.cpf | cpfcnpj}}</p></b></div><div layout=column flex=50><label>{{\'label-data\' | translate}}:</label><b><p>{{cancelamento.dataSuspensao | date: \'dd/MM/yyyy\'}}</p></b></div></div><div layout=row flex=100><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=cancelamento.justificativa required name=\"{{\'label-justificativa\' | translate}}\" maxlength=500 md-maxlength=500></textarea></md-input-container></div><div style=\"margin-top: 20%\"><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=fechar() class=md-raised>{{\'btn-fechar\' | translate}}</md-button><md-button ng-click=confirmar() class=md-raised>{{\'btn-confirmar\' | translate}}</md-button></div></div></form></div>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/partial/configuracoesRegiaoNatureza.html","<md-table-container><table md-table class=md-border-table><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-regioes\'|translate}}</th><th md-column>{{\'label-natureza\' | translate}}</th><th md-column>{{\'label-competencia\' | translate}}</th><th md-column>{{\'label-minimo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\' :\'label-valor-rs\'| translate}}</th><th md-column>{{\'label-maximo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\': \'label-valor-rs\'|translate}}</th></tr></thead><tbody md-body><!-- NORTE URBANO --><tr class=md-border-table><td rowspan=6 class=md-border-table><label>{{configuracoesRegiaoNatureza[0].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[0].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[0].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[0].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[0].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[0].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[0].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[0].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[0].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[0].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[0].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar) \" ng-model=configuracoesRegiaoNatureza[0].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[0].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[0].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[0].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[0].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORTE URBANO --><!-- NORTE RURAL --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[1].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[1].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[1].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[1].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[1].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[1].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[1].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[1].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[1].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[1].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[1].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[1].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[1].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[1].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[1].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORTE RURAL --><!-- NORDESTE URBANA --><tr class=md-border-table><td rowspan=6 class=md-border-table><label>{{configuracoesRegiaoNatureza[2].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[2].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[2].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[2].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[2].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[2].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[2].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[2].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[2].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[2].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[2].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[2].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[2].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[2].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[2].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[2].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORDESTE URBANA --><!-- NORDESTE RURAL --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[3].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[3].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[3].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[3].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[3].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[3].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[3].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[3].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[3].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[3].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[3].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[3].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[3].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[3].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[3].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORDESTE RURAL --><!-- CENTROOESTE URBANO --><tr class=md-border-table><td rowspan=6 class=md-border-table><label>{{configuracoesRegiaoNatureza[4].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[4].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[4].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[4].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[4].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[4].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[4].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[4].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[4].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[4].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[4].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[4].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[4].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[4].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[4].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[4].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- CENTROOESTE URBANO --><!-- CENTROOESTE RURAL --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[5].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[5].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[5].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[5].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[5].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[5].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[5].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[5].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[5].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[5].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[5].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[5].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[5].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[5].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[5].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- CENTROOESTE RURAL --><!-- SUDESTE URBANO --><tr class=md-border-table><td rowspan=6 class=md-border-table><label>{{configuracoesRegiaoNatureza[6].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[6].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[6].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[6].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[6].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[6].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[6].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[6].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[6].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[6].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[6].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[6].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[6].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[6].minimoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[6].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[6].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUDESTE URBANO --><!-- SUDESTE RURAL --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[7].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[7].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[7].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[7].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[7].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[7].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[7].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[7].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[7].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[7].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[7].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[7].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[7].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[7].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[7].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUDESTE RURAL --><!-- SUL URBANO --><tr class=md-border-table><td rowspan=6 class=md-border-table><label>{{configuracoesRegiaoNatureza[8].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[8].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[8].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[8].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[8].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[8].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[8].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[8].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[8].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[8].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[8].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[8].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[8].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[8].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[8].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[8].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUL URBANO --><!-- SUL RURAL --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoNatureza[9].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[9].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoNatureza[9].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoNatureza[9].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[9].maximoSuperintendente ng-change=\"configuracoesRegiaoNatureza[9].minimoSecretario = incrementarAreaValor(configuracoesRegiaoNatureza[9].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[9].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoNatureza[9].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoNatureza[9].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoNatureza[9].maximoSecretario ng-change=\"configuracoesRegiaoNatureza[9].minimoMinistro = incrementarAreaValor(configuracoesRegiaoNatureza[9].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoNatureza[9].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoNatureza[9].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUL RURAL --></tbody></table></md-table-container>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/partial/configuracoesRegiaoTodas.html","<md-table-container><table md-table class=md-border-table><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-regioes\'|translate}}</th><th md-column>{{\'label-natureza\' | translate}}</th><th md-column>{{\'label-competencia\' | translate}}</th><th md-column>{{\'label-minimo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\' :\'label-valor-rs\'| translate}}</th><th md-column>{{\'label-maximo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\': \'label-valor-rs\'|translate}}</th></tr></thead><tbody md-body><!-- NORTE TODAS --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[0].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[0].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[0].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoTodas[0].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoTodas[0].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[0].maximoSuperintendente ng-change=\"configuracoesRegiaoTodas[0].minimoSecretario = incrementarAreaValor(configuracoesRegiaoTodas[0].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[0].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoTodas[0].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoTodas[0].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[0].maximoSecretario ng-change=\"configuracoesRegiaoTodas[0].minimoMinistro = incrementarAreaValor(configuracoesRegiaoTodas[0].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[0].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoTodas[0].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORTE TODAS --><!-- NORDESTE TODAS --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[1].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[1].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[1].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoTodas[1].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoTodas[1].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[1].maximoSuperintendente ng-change=\"configuracoesRegiaoTodas[1].minimoSecretario = incrementarAreaValor(configuracoesRegiaoTodas[1].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[1].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoTodas[1].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoTodas[1].isSecretario === false ||  fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[1].maximoSecretario ng-change=\"configuracoesRegiaoTodas[1].minimoMinistro = incrementarAreaValor(configuracoesRegiaoTodas[1].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[1].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoTodas[1].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- NORDESTE TODAS --><!-- CENTROOESTE TODAS --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[2].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[2].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[2].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoTodas[2].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoTodas[2].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[2].maximoSuperintendente ng-change=\"configuracoesRegiaoTodas[2].minimoSecretario = incrementarAreaValor(configuracoesRegiaoTodas[2].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[2].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoTodas[2].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoTodas[2].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[2].maximoSecretario ng-change=\"configuracoesRegiaoTodas[2].minimoMinistro = incrementarAreaValor(configuracoesRegiaoTodas[2].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[2].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoTodas[2].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- CENTROOESTE TODAS --><!-- SUDESTE TODAS --><tr class=md-border-table><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[3].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[3].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[3].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoTodas[3].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoTodas[3].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[3].maximoSuperintendente ng-change=\"configuracoesRegiaoTodas[3].minimoSecretario = incrementarAreaValor(configuracoesRegiaoTodas[3].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[3].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoTodas[3].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoTodas[3].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[3].maximoSecretario ng-change=\"configuracoesRegiaoTodas[3].minimoMinistro = incrementarAreaValor(configuracoesRegiaoTodas[3].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[3].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoTodas[3].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUDESTE TODAS --><!-- SUL TODAS --><tr class=md-border-table><td rowspan=3 style=\"border: 1px solid #cccccc\"><label>{{configuracoesRegiaoTodas[4].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesRegiaoTodas[4].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[4].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesRegiaoTodas[4].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesRegiaoTodas[4].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[4].maximoSuperintendente ng-change=\"configuracoesRegiaoTodas[4].minimoSecretario = incrementarAreaValor(configuracoesRegiaoTodas[4].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[4].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesRegiaoTodas[4].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesRegiaoTodas[4].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesRegiaoTodas[4].maximoSecretario ng-change=\"configuracoesRegiaoTodas[4].minimoMinistro = incrementarAreaValor(configuracoesRegiaoTodas[4].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox ng-disabled=\"bloquear || isDetalhar\" class=md-primary style=\"margin-left: 1%\" ng-model=configuracoesRegiaoTodas[4].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesRegiaoTodas[4].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><!-- SUL TODAS --></tbody></table></md-table-container>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/partial/configuracoesTodasNatureza.html","<md-table-container><table md-table class=md-border-table><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-regioes\'|translate}}</th><th md-column>{{\'label-natureza\' | translate}}</th><th md-column>{{\'label-competencia\' | translate}}</th><th md-column>{{\'label-minimo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\' :\'label-valor-rs\'| translate}}</th><th md-column>{{\'label-maximo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\': \'label-valor-rs\'|translate}}</th></tr></thead><tbody md-body class=md-border-table><tr><td rowspan=6 class=md-border-table><label>{{configuracoesTodasNatureza[0].regiaoLabel}}</label></td><td rowspan=3 class=md-border-table><label>{{configuracoesTodasNatureza[0].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[0].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesTodasNatureza[0].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesTodasNatureza[0].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesTodasNatureza[0].maximoSuperintendente ng-change=\"configuracoesTodasNatureza[0].minimoSecretario = incrementarAreaValor(configuracoesTodasNatureza[0].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[0].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesTodasNatureza[0].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesTodasNatureza[0].isSecretario === false ||fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesTodasNatureza[0].maximoSecretario ng-change=\"configuracoesTodasNatureza[0].minimoMinistro = incrementarAreaValor(configuracoesTodasNatureza[0].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr class=md-border-table><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[0].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesTodasNatureza[0].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr><tr class=md-border-table><td rowspan=3 style=\"border: 1px solid #cccccc\"><label>{{configuracoesTodasNatureza[1].naturezaLabel}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[1].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSuperintendente><label>{{configuracoesTodasNatureza[1].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSuperintendente><input ng-disabled=\"configuracoesTodasNatureza[1].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesTodasNatureza[1].maximoSuperintendente ng-change=\"configuracoesTodasNatureza[1].minimoSecretario = incrementarAreaValor(configuracoesTodasNatureza[1].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr style=\"border: 2px solid #cccccc\"><td class=md-border-table ng-show=fundamentoLegal.isSecretario><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[1].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isSecretario><label>{{configuracoesTodasNatureza[1].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isSecretario><input ng-disabled=\"(configuracoesTodasNatureza[1].isSecretario === false || fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesTodasNatureza[1].maximoSecretario ng-change=\"configuracoesTodasNatureza[1].minimoMinistro = incrementarAreaValor(configuracoesTodasNatureza[1].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr style=\"border: 1px solid #cccccc\"><td class=md-border-table ng-show=fundamentoLegal.isMinistro><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" style=\"margin-left: 1%\" ng-model=configuracoesTodasNatureza[1].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td ng-show=fundamentoLegal.isMinistro><label>{{configuracoesTodasNatureza[1].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table ng-show=fundamentoLegal.isMinistro><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-integrador/pages/fundamento-legal/view/partial/configuracoesTodasTodas.html","<md-table-container><table md-table class=md-border-table><thead md-head><tr md-row class=upload-head><th md-column>{{\'label-regioes\'|translate}}</th><th md-column>{{\'label-natureza\' | translate}}</th><th md-column>{{\'label-competencia\' | translate}}</th><th md-column>{{\'label-minimo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\' :\'label-valor-rs\'| translate}}</th><th md-column>{{\'label-maximo\' | translate}} {{fundamentoLegal.criterio==AREA_IMOVEL_PARCELA ? \'label-area-ha\': \'label-valor-rs\'|translate}}</th></tr></thead><tbody md-body><tr><td rowspan=4 class=md-border-table><label>{{configuracoesTodasTodas[0].regiaoLabel}}</label></td><td rowspan=4 class=md-border-table><label>{{configuracoesTodasTodas[0].naturezaLabel}}</label></td></tr><tr ng-if=\"fundamentoLegal.isSuperintendente === true \" class=\"md-border-table md-config-table\"><td class=md-border-table><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" ng-model=configuracoesTodasTodas[0].isSuperintendente name=\"{{\'label-superintendente\' | translate}}\" flex=25><label>{{\'label-superintendente\' | translate}}</label></md-checkbox></td><td class=texto-centro><label>{{configuracoesTodasTodas[0].minimoSuperintendente | currency : \"\"}}</label></td><td class=md-border-table><input ng-disabled=\"configuracoesTodasTodas[0].isSuperintendente === false || (bloquear || isDetalhar)\" ng-model=configuracoesTodasTodas[0].maximoSuperintendente ng-change=\"configuracoesTodasTodas[0].minimoSecretario = incrementarAreaValor(configuracoesTodasTodas[0].maximoSuperintendente)\" name=\"{{\'label-superintendente\' | translate}}\" ui-number-mask=2></td></tr><tr ng-if=\"fundamentoLegal.isSecretario === true\" class=md-border-table><td class=md-border-table><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" ng-model=configuracoesTodasTodas[0].isSecretario name=\"{{\'label-secretario\' | translate}}\" flex=25><label>{{\'label-secretario\' | translate}}</label></md-checkbox></td><td><label>{{configuracoesTodasTodas[0].minimoSecretario | currency : \"\"}}</label></td><td class=md-border-table><input ng-disabled=\"(configuracoesTodasTodas[0].isSecretario === false ||\n               fundamentoLegal.isSuperintendente === true && (fundamentoLegal.isMinistro === false || !fundamentoLegal.isMinistro)\n               && fundamentoLegal.isSecretario === true) || (bloquear || isDetalhar)\" ng-model=configuracoesTodasTodas[0].maximoSecretario ng-change=\"configuracoesTodasTodas[0].minimoMinistro = incrementarAreaValor(configuracoesTodasTodas[0].maximoSecretario)\" name=\"{{\'label-secretario\' | translate}}\" ui-number-mask=2></td></tr><tr ng-if=\"fundamentoLegal.isMinistro=== true\" class=md-border-table><td class=md-border-table><md-checkbox class=md-primary ng-disabled=\"bloquear || isDetalhar\" ng-model=configuracoesTodasTodas[0].isMinistro name=\"{{\'label-ministro\' | translate}}\" flex=25><label>{{\'label-ministro\' | translate}}</label></md-checkbox></td><td><label>{{configuracoesTodasTodas[0].minimoMinistro | currency : \"\"}}</label></td><td class=md-border-table><input disabled name=\"{{\'label-ministro\' | translate}}\"></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-integrador/pages/home/view/dialogs/informe.dialog.html","<md-dialog aria-label=Informes flex=60 ng-cloak><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>Informes</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><md-content><div class=md-dialog-content ng-show=controleExibicao><div class=informe-item ng-repeat=\"item in informes\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><p class=informe-data>{{item.dataPublicacao | date : \'dd/MM/yyyy\'}}</p><h2 class=informe-titulo>{{item.titulo}}</h2><span class=informe-descricao ng-bind-html=descricaoExtensa(item.corpo)></span><div layout=row layout-align=\"end end\"><md-button class=\"btn-continue-lendo continue-informe\" ng-click=exibirDescricao(item)>continue lendo</md-button></div></div></div><md-table-pagination style=\"margin: auto; background-color: #5bf869\" ng-show=controleExibicao ng-show=\"informes.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findTodosInformes md-page-select></md-table-pagination></md-content><div class=md-dialog-content ng-show=!controleExibicao><div class=informe-item><h2 class=informe-titulo-v2>{{informeSelecionado.titulo}}</h2><p class=informe-data-v2>Publicado em {{informeSelecionado.dataPublicacao | date : \'dd/MM/yyyy\'}}</p><div ng-if=informeSelecionado.imagem class=informe-caixa-img><img ng-src={{gerarSrcImagem(informeSelecionado.imagem)}} height=150 width=300></div><span ng-bind-html=informeSelecionado.corpo></span><md-button class=\"md-primary md-raised\" ng-click=voltar()>Voltar</md-button></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-integrador/pages/home/view/dialogs/mensagem.dialog.html","<md-dialog aria-label=Mensagens flex=60 ng-cloak><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>{{mensagemSelecionada.assunto}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><p ng-bind-html=mensagemSelecionada.mensagem><md-button class=\"md-primary md-raised\" ng-click=voltarParaDlgMensagens($event)>Voltar</md-button></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-integrador/pages/home/view/dialogs/mensagens.dialog.html","<md-dialog aria-label=Mensagens flex=60 ng-cloak><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>Mensagens</h2><span flex></span><md-button class=\"md-icon-button md-icon-fechar\" ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><p class=mensagem ng-repeat=\"item in mensagens\" ng-model-options=\"{trackBy: \'$value.codigo\'}\" ng-click=\"mensagemLida($index); exibirMensagem($event, $index)\"><a ng-class=\"(item.lida == false ? \'msg-nao-lida\' : \'msg-lida\')\" href=#>{{item.dataEnvio | date : \'dd/MM/yyyy\'}} - {{item.assunto}}</a></p></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-integrador/pages/home/view/dialogs/pendencias.dialog.html","<md-dialog aria-label=Pendências ng-cloak><form><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>{{tituloPendencia}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row ng-show=\"pendencias.length>0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>Situação</th><th md-column>Quantidade</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"pendencia in pendencias\"><td md-cell class=celula><a ng-click=abrirPendencia(pendencia) ng-href={{pendencia.caminhoFuncionalidade}} target=_blank>{{pendencia.pendencia}}</a></td><td md-cell class=celula>{{pendencia.quantidade}}</td></tr></tbody></table></md-table-container></md-content></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/informes/view/dialog/preVisualizar.html","<md-dialog aria-label=Informe ng-cloak><form><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>Visualizar Informe</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=column><md-content flex><h2>{{informe.titulo}}</h2><h5>Data de Publicação: {{informe.dataPublicacao ? informe.dataPublicacao : dataPublicacaoInforme | date : \'dd/MM/yyyy\'}}</h5></md-content><md-content flex ng-show=imagem><div layout=row layout-align=\"center center\"><img alt=\"\" title={{imagem.name}} src={{imagem.src}} style=\"width: 336px; height: 123px\"></div></md-content><md-content flex><div ng-bind-html=renderCorpoHtml()></div></md-content></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/partials/checklistEspecífico.html","<md-dialog aria-label=\"{{\'label-checklist-configurar-componetes\' | translate | uppercase}}\" flex=60 ng-cloak><md-toolbar><div class=md-toolbar-tools><h2 ng-if=!checkListCtrl.modoConsulta>{{\'label-item-checklist-especifico\' | translate}}</h2><h2 ng-if=checkListCtrl.modoConsulta>{{\'label-consulta-checklist-especifico\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=checkListCtrl.fechar()><md-icon md-svg-src=assets/img/icons/ic_close_white_24px.svg aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=column><label>{{\'label-item-checklist-especifico\' | translate}}</label><md-input-container flex=100 class=textangular><div text-angular ng-model=checkListCtrl.checkList.descricao ta-toolbar=checkListCtrl.camposEdicaoTextAngular ta-max-text=400 md-select-on-focus></div></md-input-container><md-input-container class=md-block flex=100><label>{{\'label-fundamento-legal\' | translate}}</label><md-select ng-model=checkListCtrl.checkList.fundamentoLegalList multiple ng-model-options=\"{trackBy: \'$value.descricao\'}\"><md-option ng-repeat=\"item in checkListCtrl.fundamentosLegaisCadastrados\" ng-value=item><span ng-bind-html=checkListCtrl.formatarString(item.descricao)></span></md-option></md-select></md-input-container><div layout=row layout-align=\"end end\" ng-if=\"!checkListCtrl.editar && !checkListCtrl.modoConsulta\"><md-button class=md-raised ng-click=checkListCtrl.adicionarCheckList()>{{\'label-inserir\' | translate}}</md-button><md-button class=md-raised ng-click=checkListCtrl.ativarModoConsulta()>{{\'label-modo-consulta\' | translate}}</md-button></div><div layout=row layout-align=\"end end\" ng-if=\"!checkListCtrl.editar && checkListCtrl.modoConsulta\"><md-button class=md-raised ng-click=checkListCtrl.consultarCheckList()>{{\'label-consultar\' | translate}}</md-button><md-button class=md-raised ng-click=checkListCtrl.ativarModoInclusao()>{{\'label-modo-inclusao\' | translate}}</md-button></div><div layout=row layout-align=\"end end\" ng-if=checkListCtrl.editar><md-button class=md-raised ng-click=checkListCtrl.confirmarEdicao()>{{\'label-confirmar-edicao\' | translate}}</md-button><md-button class=md-raised ng-click=checkListCtrl.cancelarEdicao()>{{\'label-cancelar-edicao\' | translate}}</md-button></div><div ng-if=\"!checkListCtrl.modoConsulta && checkListCtrl.instrumento.listChecklist.length > 0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th></th><th md-column class=texto-esquerdo>{{\'label-item\' | translate}}</th><th md-column class=texto-centro>{{\'label-fundamento-legal\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in checkListCtrl.instrumento.listChecklist\n                        | limitTo: checkListCtrl.tabelaCheckList.limit:\n                        (checkListCtrl.tabelaCheckList.page - 1) * checkListCtrl.tabelaCheckList.limit\"><td md-cell class=\"margin-top tamanho-setas\"><div flex=10><i class=\"material-icons hand\" ng-click=\"checkListCtrl.ordemCampos($index, $index-1, checkListCtrl.instrumento.listChecklist)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"checkListCtrl.ordemCampos($index, $index+1, checkListCtrl.instrumento.listChecklist)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div layout=row layout-align=start><label ng-bind-html=item.descricao></label></div></td><td md-cell class=input-esquerda><div layout=row layout-align=center ng-repeat=\"fundamento in item.fundamentoLegalList\"><span ng-bind-html=checkListCtrl.formatarString(fundamento.descricao)></span></div></td><td md-cell layout=row layout-align=center><div layout=row layout-align=center class=margin-top><i class=\"material-icons hand\" ng-click=\"checkListCtrl.editarObjeto(checkListCtrl.instrumento.listChecklist, item )\">mode_edit</i><md-switch class=md-primary ng-if=checkListCtrl.formatoEdicao() ng-model=item.ativo></md-switch><i class=\"material-icons hand\" ng-if=!item.registroVinculado ng-click=\"checkListCtrl.removerItem($event, item, checkListCtrl.instrumento.listChecklist)\">delete</i></div></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"checkListCtrl.instrumento.listChecklist.length > 0\" md-limit=checkListCtrl.tabelaCheckList.limit md-limit-options=checkListCtrl.tabelaCheckList.limitsPage md-page=checkListCtrl.tabelaCheckList.page md-total={{checkListCtrl.instrumento.listChecklist.length}} md-on-paginate=true></md-table-pagination></md-content></div><div ng-if=\"checkListCtrl.modoConsulta && checkListCtrl.checkListConsulta.length > 0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th></th><th md-column class=texto-esquerdo>{{\'label-item\' | translate}}</th><th md-column class=texto-centro>{{\'label-fundamento-legal\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in checkListCtrl.checkListConsulta\n                        | limitTo: checkListCtrl.tabelaCheckList.limit:\n                        (checkListCtrl.tabelaCheckList.page - 1) * checkListCtrl.tabelaCheckList.limit\"><td md-cell class=\"margin-top tamanho-setas\"><div flex=10><i class=\"material-icons hand\" ng-click=\"checkListCtrl.ordemCampos($index, $index-1, checkListCtrl.instrumento.listChecklist)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"checkListCtrl.ordemCampos($index, $index+1, checkListCtrl.instrumento.listChecklist)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div layout=row layout-align=start><label ng-bind-html=item.descricao></label></div></td><td md-cell class=input-esquerda><div layout=row layout-align=center ng-repeat=\"fundamento in item.fundamentoLegalList\"><span ng-bind-html=checkListCtrl.formatarString(fundamento.descricao)></span></div></td><td md-cell layout=row layout-align=center><div layout=row layout-align=center class=margin-top><i class=\"material-icons hand\" ng-click=\"checkListCtrl.editarObjeto(checkListCtrl.checkListConsulta, item )\">mode_edit</i><md-switch class=md-primary ng-if=checkListCtrl.formatoEdicao() ng-model=item.ativo></md-switch><i class=\"material-icons hand\" ng-if=!item.registroVinculado ng-click=\"checkListCtrl.removerItem($event, item, checkListCtrl.instrumento.listChecklist)\">delete</i></div></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"checkListCtrl.instrumento.listChecklist.length > 0\" md-limit=checkListCtrl.tabelaCheckList.limit md-limit-options=checkListCtrl.tabelaCheckList.limitsPage md-page=checkListCtrl.tabelaCheckList.page md-total={{checkListCtrl.instrumento.listChecklist.length}} md-on-paginate=true></md-table-pagination></md-content></div><div class=margin-top layout=row layout-align=\"end center\" ng-if=!checkListCtrl.modoConsulta><md-button class=md-raised ng-click=checkListCtrl.confirmar()>{{\'label-confirmar\' | translate}}</md-button></div></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/partials/fundamentoLegal.html","<md-dialog aria-label=\"{{\'label-checklist-configurar-componetes\' | translate | uppercase}}\" flex=60 ng-cloak><md-toolbar><div class=md-toolbar-tools><h2 ng-if=!fundamentoCtrl.modoConsulta>{{\'label-fundamento-legal\' | translate}}</h2><h2 ng-if=fundamentoCtrl.modoConsulta>{{\'label-consulta-fundamento-legal\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=fundamentoCtrl.fechar()><md-icon md-svg-src=assets/img/icons/ic_close_white_24px.svg aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row class=align-tabela><md-content flex><div layout=column><label>{{\'label-fundamento-legal\' | translate}}</label><md-input-container flex=100 class=textangular><div text-angular required ta-toolbar=fundamentoCtrl.camposEdicaoTextAngular ng-model=fundamentoCtrl.fundamentoLegal.descricao ta-max-text=100 md-select-on-focus></div></md-input-container><div layout=row layout-align=\"end end\" ng-if=\"!fundamentoCtrl.editar && !fundamentoCtrl.modoConsulta\"><md-button class=md-raised ng-click=fundamentoCtrl.adicionarFundamentoLegal()>{{\'label-inserir\' | translate}}</md-button><md-button class=md-raised ng-click=fundamentoCtrl.ativarModoConsulta()>{{\'label-modo-consulta\' | translate}}</md-button></div><div layout=row layout-align=\"end end\" ng-if=\"!fundamentoCtrl.editar && fundamentoCtrl.modoConsulta\"><md-button class=md-raised ng-click=fundamentoCtrl.consultarFundamentoLegal()>{{\'label-consultar\' | translate}}</md-button><md-button class=md-raised ng-click=fundamentoCtrl.ativarModoInclusao()>{{\'label-modo-inclusao\' | translate}}</md-button></div><div layout=row layout-align=\"end end\" ng-if=fundamentoCtrl.editar><md-button class=md-raised ng-click=fundamentoCtrl.confirmarEdicao()>{{\'label-confirmar-edicao\' | translate}}</md-button><md-button class=md-raised ng-click=fundamentoCtrl.cancelarEdicao()>{{\'label-cancelar-edicao\' | translate}}</md-button></div><div ng-if=\"!fundamentoCtrl.modoConsulta && fundamentoCtrl.instrumento.listFundamentoLegais.length > 0 \"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th></th><th md-column class=texto-esquerdo>{{\'label-fundamento-legal\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"fundamento in fundamentoCtrl.instrumento.listFundamentoLegais\n                        | limitTo: fundamentoCtrl.tabelaFundamentos.limit:\n                        (fundamentoCtrl.tabelaFundamentos.page - 1) * fundamentoCtrl.tabelaFundamentos.limit\"><td md-cell class=\"margin-top tamanho-setas\"><div flex=10><i class=\"material-icons hand\" ng-click=\"fundamentoCtrl.ordemCampos($index, $index-1, fundamentoCtrl.instrumento.listFundamentoLegais)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"fundamentoCtrl.ordemCampos($index, $index+1, fundamentoCtrl.instrumento.listFundamentoLegais)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=input-esquerda><div layout=row layout-align=start><label ng-bind-html=fundamento.descricao></label></div></td><td md-cell layout=row layout-align=center><div layout=row layout-align=center class=margin-top><i class=\"material-icons hand\" ng-click=\"fundamentoCtrl.editarObjeto(fundamentoCtrl.instrumento.listFundamentoLegais, fundamento )\">mode_edit</i><md-switch class=md-primary ng-model=fundamento.ativo ng-if=fundamentoCtrl.formatoEdicao()></md-switch><i class=\"material-icons hand\" ng-if=!fundamento.registroVinculado ng-click=\"fundamentoCtrl.removerItem($event, fundamento, fundamentoCtrl.instrumento.listFundamentoLegais)\">delete</i></div></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"fundamentoCtrl.instrumento.listFundamentoLegais.length > 0\" md-limit=fundamentoCtrl.tabelaFundamentos.limit md-limit-options=fundamentoCtrl.tabelaFundamentos.limitsPage md-page=fundamentoCtrl.tabelaFundamentos.page md-total={{fundamentoCtrl.instrumento.listFundamentoLegais.length}} md-on-paginate=true></md-table-pagination></md-content></div><div ng-if=\"fundamentoCtrl.modoConsulta && fundamentoCtrl.fundamamentoLegalConsulta.length > 0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th></th><th md-column class=texto-esquerdo>{{\'label-fundamento-legal\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"fundamento in fundamentoCtrl.fundamamentoLegalConsulta\n                        | limitTo: fundamentoCtrl.tabelaFundamentos.limit:\n                        (fundamentoCtrl.tabelaFundamentos.page - 1) * fundamentoCtrl.tabelaFundamentos.limit\"><td md-cell class=\"margin-top tamanho-setas\"></td><td md-cell class=input-esquerda><div layout=row layout-align=start><label ng-bind-html=fundamento.descricao></label></div></td><td md-cell layout=row layout-align=center><div layout=row layout-align=center class=margin-top><i class=\"material-icons hand\" ng-click=\"fundamentoCtrl.editarObjeto(fundamentoCtrl.fundamamentoLegalConsulta, fundamento )\">mode_edit</i><md-switch class=md-primary ng-model=fundamento.ativo ng-if=fundamentoCtrl.formatoEdicao()></md-switch><i class=\"material-icons hand\" ng-if=!fundamento.registroVinculado ng-click=\"fundamentoCtrl.removerItem($event, fundamento, fundamentoCtrl.fundamamentoLegalConsulta)\">delete</i></div></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"fundamentoCtrl.fundamamentoLegalConsulta.length > 0\" md-limit=fundamentoCtrl.tabelaFundamentos.limit md-limit-options=fundamentoCtrl.tabelaFundamentos.limitsPage md-page=fundamentoCtrl.tabelaFundamentos.page md-total={{fundamentoCtrl.fundamamentoLegalConsulta.length}} md-on-paginate=true></md-table-pagination></md-content></div><div class=margin-top layout=row layout-align=\"end center\" ng-if=!fundamentoCtrl.modoConsulta><md-button class=md-raised ng-click=fundamentoCtrl.confirmar()>{{\'label-confirmar\' | translate}}</md-button></div></div></md-content></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/partials/justificativa.html","<md-dialog aria-label=Suspender flex=60 ng-cloak><form name=justificativaCtrl.formSuspender><md-toolbar><div class=\"md-toolbar-tools text-center\"><h2>{{\'label-suspensao\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=justificativaCtrl.cancelar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row flex=100 layout-margin><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}*</label><textarea ng-model=justificativaCtrl.funcionalidade.justificativa required name=justificativa maxlength=200 md-maxlength=200 ng-required=true></textarea></md-input-container></div><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=justificativaCtrl.cancelar() class=md-raised>Cancelar</md-button><md-button ng-click=justificativaCtrl.suspenderInstrumento() class=md-raised>Confirmar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/partials/removerFundamento.html","<md-dialog aria-label=\"Remover Fundamento Legal\" flex=40 ng-cloak><form name=removerFundamento id=teste><md-dialog-content><div class=md-dialog-content><h2 class=md-title>{{\'label-remover-fundamento-legal\' | translate}}</h2><p>{{\'label-confirmacao-remover-fundamento\' | translate}}</p></div><div layout=row flex=100 layout-align=\"end center\" class=md-dialog-actions><md-button ng-click=removerFundamentoCtrl.fechar() class=md-raised><span>Cancelamento</span></md-button><md-button ng-click=removerFundamentoCtrl.confirmar() class=md-raised><span>Confirmação</span></md-button></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/pages/instrumento-funcionalidade/view/partials/suspenderInstrumento.html","<div style=\"margin: 20px\"><form name=formSuspender><titulo prefixo-titulo=\"\'Suspender\'\"></titulo><div layout=row><div layout=column flex=50><label>{{\'label-responsavel\' | translate}}:</label><b><p>{{responsavel.nome}} / {{responsavel.cpf | cpfcnpj}}</p></b></div><div layout=column flex=50><label>{{\'label-data\' | translate}}:</label><b><p>{{suspender.dataSuspensao | date: \'dd/MM/yyyy\'}}</p></b></div></div><div layout=row flex=100><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=suspender.justificativa required name=\"{{\'label-justificativa\' | translate}}\" maxlength=500 md-maxlength=500></textarea></md-input-container></div><div style=\"margin-top: 20%\"><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=cancelar() class=md-raised>{{\'btn-cancelar\' | translate}}</md-button><md-button ng-click=confirmar() class=md-raised>{{\'btn-confirmar\' | translate}}</md-button></div></div></form></div>");
$templateCache.put("scripts-integrador/pages/orientacao-tecnica/view/partials/modalLink.html","<md-dialog aria-label=\"Adicionar Link de Referência\" flex=40 ng-cloak><form ng-submit=adicionarLink()><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>Adicionar Link de Referência</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content layout=column layout-margin=5><md-input-container><label>Link</label><input ng-model=link.descricaoLink required ng-disabled=consultar></md-input-container><md-input-container><label>Tipo de Ato/Normativo</label><md-select ng-model=link.tipoOrientacaoTecnica required ng-disabled=consultar><md-option ng-repeat=\"tipo in tiposOrientacaoTecnica\" ng-selected=\"tipo.id == link.tipoOrientacaoTecnica.id\" ng-value=tipo>{{tipo.descricao}}</md-option></md-select><div class=md-errors-spacer></div></md-input-container><md-input-container><label>Descrição do Ato/Normativo</label><input ng-model=link.descricaoOrientacaoTecnica maxlength=300 md-maxlength=300 required ng-disabled=consultar></md-input-container><div layout=row><md-input-container flex=50><label>Número do Ato/Normativo</label><input type=text ng-model=link.numeroOrientacaoTecnica required ng-disabled=consultar></md-input-container><md-input-container flex=50><label>Data do Ato/Normativo</label><md-datepicker ng-model=link.dateOrientacaoTecnica required ng-disabled=consultar></md-datepicker></md-input-container></div><div layout=row layout-align=end><input class=\"md-raised md-button md-ink-ripple botao-formulario-modal\" type=submit value=inserir ng-if=!consultar></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/app/directives/secoes/templates/partials/modalAdicionarEditorHtml.html","<md-dialog aria-label={{titulo}} flex=60 ng-cloak><form name=form><md-toolbar><div class=md-toolbar-tools><h2>{{titulo}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row><label>{{descricao}}</label></div><div layout=row><text-angular ng-model=item.descricao name=corpo ta-toolbar-button-class=\"btn btn-default\" ta-toolbar-button-active-class=active></text-angular></div><div layout=row layout-sm=column layout-align=\"end center\" class=top-2><md-button class=md-raised ng-click=fechar()>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised ng-click=confirmar()>{{\'btn-confirmar\' | translate}}</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/app/directives/secoes/templates/partials/modalClausulas.html","<md-dialog aria-label=\"{{\'label-checklist-analise-tecnica\' | translate}}\" flex=60 ng-cloak id=su-integrador><form name=formClausula ng-submit=confirmar() novalidate><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-clausulas\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro></th><th md-column class=texto-centro>{{\'label-clausulas\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"clausula in listClausulas\"><td md-cell class=\"celula tamanho-setas\"><div flex=10><i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index-1, listClausulas)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemCampos($index, $index+1, listClausulas)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell class=\"celula tabela-clausula\" class=tamanho-setas><md-input-container layout=row flex-offset=10 flex><label>{{getNumeroPorExtenso($index)}}</label><input type=text required ng-model=clausula.descricao ng-maxlength=100 maxlength=100></md-input-container></td><td md-cell class=\"celula deletar\" layout-aling=\"center center\"><a href=\"\" ng-click=remover($index); style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22><md-tooltip>{{\'excluir\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></md-content></div><div layout=row layout-align=end><md-button class=md-raised style=\"margin-left: 0\" ng-click=adicionarClausula()>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row layout-sm=column layout-align=\"end center\" class=top-2><md-button class=md-raised ng-click=fechar()>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised type=submit>{{\'btn-confirmar\' | translate}}</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-integrador/app/directives/utilitarios/titulo/template/titulo.html","<h2 class=tituloFormulario>{{titulo}}</h2>");}]);
(function () {
    'use strict';

    angular.module('su-cadastro-imovel', [
        'su-componentes', 'ciMapaDirective']);

})();

(function () {
    "use strict";
    fieldSetTerceiros.$inject = ["$mdDialog", "imovelService", "ReceitaService", "MensagensService", "PaginacaoFactory", "ABA_PROPRIETARIO", "NotificadorService", "NOTIFICADORES"];
    angular.module("su-cadastro-imovel").directive("fieldSetTerceiros", fieldSetTerceiros);

    function fieldSetTerceiros($mdDialog, imovelService, ReceitaService, MensagensService, PaginacaoFactory,ABA_PROPRIETARIO, NotificadorService, NOTIFICADORES) {
        return {
            templateUrl: 'scripts-cadastro-imovel/app/directives/secoes-formulario/proprietario/fieldset-terceiros/view/fieldset-terceiros.html',
            restrict: 'E',
            scope: {
                imovel:'=',
                proprietario: '=',
                isDetail: '=',
                tipoProprietario: '@',
                desabilitar: '='
            },
            controller: function () {
                var vm = this;

                vm.terceiroProprietario = {};
            },
            controllerAs: "ctrl",
            link: function (scope, element, attrs, ctrl) {
                var vm = scope,

                    // Tipos proprietário real
                    ID_TERCEIROS_PROPRIETARIO_REAL = 6,

                    // Formas de incorporação
                    ID_IMOVEL_DE_TERCEIROS = 5,

                    //Tipos de aquisicao
                    ID_TIPO_AQUISICAO_HERANCA_JACENTE = 6,
                    ID_TIPO_AQUISICAO_RECEBIMENTO_EM_DOACAO = 8,
                    ID_TIPO_AQUISICAO_ARREMATACAO_JUDICIAL = 10,
                    ID_TIPO_AQUISICAO_REGISTRO_POR_APOSSAMENTO = 11,
                    ID_TIPO_AQUISICAO_DESAPROPRIACAO_JUDICIAL = 16;

                vm.proprietario = vm.proprietario ? vm.proprietario : {};
                vm.proprietario.pessoas = vm.proprietario.pessoas ? vm.proprietario.pessoas : [];
                vm.ordenacao = null;
                vm.paginacao = PaginacaoFactory.getDefault();

                vm.isImovelDeTerceiros = function () {
                    var isImovelDeTerceiros;
                    if (vm.imovel.incorporacao.tipoIncorporacao) {
                        isImovelDeTerceiros  = vm.imovel.incorporacao.tipoIncorporacao.id === ID_IMOVEL_DE_TERCEIROS;
                        var proprietarioRealUniao = undefined;
                        if (isImovelDeTerceiros) {
                            for (var i = 0; i < vm.$parent.tiposProprietarioReal.length; i++) {
                                if (vm.$parent.tiposProprietarioReal[i].id === ID_TERCEIROS_PROPRIETARIO_REAL) {
                                    proprietarioRealUniao = vm.$parent.tiposProprietarioReal[i];
                                    break;
                                }
                            }
                            vm.proprietario.tipoProprietarioReal = proprietarioRealUniao;
                        }
                    }
                    return isImovelDeTerceiros;

                };

                vm.isTipoProprietarioTerceiros = function () {
                    if (vm.proprietario &&  vm.proprietario.tipoProprietarioReal) {
                        return vm.proprietario.tipoProprietarioReal.id === ID_TERCEIROS_PROPRIETARIO_REAL;
                    }
                };

                function onlyNumbers(string) {
                    if (!string) {
                        return '';
                    }
                    return string.replace(/[^0-9]/g, '');
                }

                vm.proprietarioTerceiroIgualAnterior = function (documentoTerceiro) {
                    return vm.imovel.proprietariosTerceiros.some(function (proprietario) {
                        return onlyNumbers(proprietario.cpf) === onlyNumbers(documentoTerceiro)
                    });
                }

                vm.validaNumeroDocumento = function () {
                    ctrl.isNumeroDocumentoValid = false;
                    var numeroDocumento = ctrl.terceiroProprietario.numeroDocumento;
                    if (numeroDocumento) {
                        if(vm.proprietarioTerceiroIgualAnterior(numeroDocumento)){
                            ctrl.terceiroProprietario.numeroDocumento = '';
                            MensagensService.exibirMensagemErro("O proprietário anterior não pode ser o mesmo proprietário do imóvel informado");
                            return;
                        }
                        if (numeroDocumento.length > 11) {
                            ctrl.isNumeroDocumentoValid = imovelService.validarCnpj(numeroDocumento);
                        } else if (numeroDocumento.length == 11) {
                            ctrl.isNumeroDocumentoValid = imovelService.validaCpf(numeroDocumento);
                        }

                        if (ctrl.isNumeroDocumentoValid) {   
                          ReceitaService.get({cpfCnpj: numeroDocumento}).$promise.then(function(data){
                            if (data && data.nomeProprietarioReceita) {
                                ctrl.terceiroProprietario.nomeProprietarioReceita = data.nomeProprietarioReceita;
                            }else{
                                ctrl.terceiroProprietario.numeroDocumento = '';
                                MensagensService.exibirMensagemErro('CPF/CNPJ não cadastrado');
                            }
                            }, function errorCallback(){
                              vm.loading = false;
                              vm.carregando - false;
                              ctrl.terceiroProprietario.nomeProprietarioReceita = undefined;
                              MensagensService.exibirMensagemErro("CPF ou CNPJ inválido.");
                            });

                        } else if (numeroDocumento.length == 14 || numeroDocumento.length == 11 ) {
                            ctrl.terceiroProprietario.nomeProprietarioReceita = undefined;
                            MensagensService.exibirMensagemErro("CPF ou CNPJ inválido.");
                            return;
                        } else {
                            ctrl.terceiroProprietario.nomeProprietarioReceita= undefined;
                        }
                    }
                };

                vm.mostraOpcaoTerceiroNaoPossuiCpfOuCnpj = function () {
                    var idTipoAquisicao;
                    if ( vm.imovel.incorporacao.tipoAquisicao) {
                        idTipoAquisicao = vm.imovel.incorporacao.tipoAquisicao.id;
                    }
                    return  (idTipoAquisicao === ID_TIPO_AQUISICAO_DESAPROPRIACAO_JUDICIAL ||
                        idTipoAquisicao === ID_TIPO_AQUISICAO_ARREMATACAO_JUDICIAL ||
                        idTipoAquisicao === ID_TIPO_AQUISICAO_HERANCA_JACENTE ||
                        idTipoAquisicao === ID_TIPO_AQUISICAO_REGISTRO_POR_APOSSAMENTO) &&
                        vm.tipoProprietario === 'anterior';

                };

                vm.terceiroNaoPossuiCpfOuCnpj = function () {
                    if (vm.proprietario) {
                        return vm.proprietario.naoPossuiCpfOuCnpj;
                    }
                };

                vm.isInputCpfCnpjTerceirosObrigatorio  = function () {
                    return  !(vm.proprietario.pessoas && vm.proprietario.pessoas.length > 0)
                             && (vm.imovel.incorporacao.tipoAquisicao);
                };

                vm.adicionaTerceiro = function () {
                    if (ctrl.terceiroProprietario.numeroDocumento && ctrl.terceiroProprietario.nomeProprietarioReceita) {
                        var terceiro = angular.copy(ctrl.terceiroProprietario);
                        var c = 0;
                        if (ctrl.isNumeroDocumentoValid && terceiro.numeroDocumento && vm.proprietario.pessoas) {
                            for (var i = 0; i < vm.proprietario.pessoas.length; i++) {
                                if (vm.proprietario.pessoas[i].numeroDocumento == terceiro.numeroDocumento) {
                                    c++;
                                }
                            }
                        }
                        if (c == 0) {
                          if (vm.proprietario.pessoas){
                            //vm.proprietario.pessoas.push(terceiro);
                            vm.proprietario.pessoas.unshift(terceiro);
                          } else {
                            vm.proprietario.pessoas = terceiro;
                          }
                          vm.terceiroProprietario = undefined;
                        }
                    } else {
                        MensagensService.exibirMensagemCamposObrigatorios();
                        return;
                    }
                };


                vm.removerTerceiro = function(pessoa){
                    var pessoas = vm.proprietario.pessoas;
                    if (pessoas && pessoas.length > 0) {
                        var confirm = $mdDialog.confirm({focusOnOpen: false})
                            .textContent("Deseja remover este item?")
                            .ariaLabel("Excluir item")
                            .cancel("Não")
                            .ok("Excluir");

                        $mdDialog.show(confirm).then(function(){
                            var _index = pessoas.indexOf(pessoa);
                            pessoas.splice(_index, 1);
                            MensagensService.exibirMensagemSucesso("Item excluído com sucesso.");
                            $mdDialog.hide();

                        }, function(){
                            $mdDialog.cancel();
                        });
                    }
                };

                NotificadorService.subscribe(scope, function(evento, escopo, tabIndex) {
                  if (tabIndex == ABA_PROPRIETARIO){
                    vm.terceiroProprietario = {};
                    ctrl.terceiroProprietario = {};

                  }
                }, NOTIFICADORES.LIMPAR_ABA);

            }
        };
    }

}());

(function() {
  "use strict";
  directive.$inject = ["cpfCnpjMaskService"];
  angular.module("su-cadastro-imovel").directive("dadosAutarquia", directive);

  function directive(cpfCnpjMaskService) {

      return {
          templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/proprietario/autarquia/view/dados-autarquia.html",
          restrict: 'E',
          scope: {
              proprietario: "=",
              isDetail: "=",
              isLocatario: "=",
              imovel: "=",
              desabilitar: "="
          },
          controller: ["$rootScope", function($rootScope) {
              var vm = this;
              if ($rootScope.usuarioLogado) {
                  vm.codigoUGUsuarioLogado = $rootScope.usuarioLogado.idOrganizacao;
                  vm.ugUsuarioLogado = $rootScope.usuarioLogado.organizacao;
              }

          }],
          controllerAs: "ctrl",
          link: function($scope) {
              var vm = $scope,
                  init;

              init = function() {

                  // if(vm.proprietario){

                  //   if(vm.isLocatario == true){
                  //     vm.nome = vm.proprietario.nomeLocatario;
                  //     vm.ug = vm.proprietario.ugLocatario;
                  //     vm.cnpj = vm.proprietario.cnpjLocatario;
                  //   }else{
                  //     vm.nome = vm.proprietario.nomeAutarquia;
                  //     vm.ug = vm.proprietario.ugAutarquia;
                  //     vm.cnpj = vm.proprietario.cnpjAutarquia;
                  //   }
                  // } else {
                  //   vm.ug = '';
                  //   vm.nome = '';
                  //   vm.cnpj = '';
                  // }


              };


              vm.mascaraCnpj = function(cnpj, name) {
                  if (cnpj != null && name != null) {
                      cnpj = cpfCnpjMaskService.getMaskCnpj(cnpj);
                      document.getElementsByName(name)[0].value = cnpj;
                  }
              };

              vm.isInputUgCnpjObrigatorio  = function () {
                if(vm.isLocatario){
                    return !(vm.proprietario.ugLocatario != null);
                }else{
                    return !(vm.proprietario.ugAutarquia != null)
                         && (vm.imovel.incorporacao.tipoAquisicao);
                }
              };

              init();

          }
      };
  }
})();

( function () {
    "use strict";
    angular.module("su-cadastro-imovel").service("MatriculaService", ["MensagensService", function(MensagensService) {

        /**
         * Verifica se a data da Matrícula é menor que a data do Registro. <br />
         * Caso seja verdade, a função lança uma mensagem e encerra o fluxo. <br />
         * Referência: US002 - Critério 31
         */
        function validaDataMatricula (matricula) {
            if (matricula.dataMatricula < matricula.dataRegistro) {
                var _mensagem = "A data da matrícula não pode ser anterior a data do registro.";
                MensagensService.exibirMensagemErro(_mensagem);
                throw _mensagem;
            }
        }

        return {
            validaDataMatricula: validaDataMatricula
        };
    }]);
})();

(function () {
  'use strict';
  matriculaTranscricaoFieldset.$inject = ["UfsService", "MunicipioService", "CartorioService", "MatriculaService", "$mdDialog", "PesquisarImovelMatricula", "MensagensService", "PaginacaoFactory", "NotificadorService", "NOTIFICADORES", "ABA_DADOS_GERAIS"];
  angular
    .module('su-cadastro-imovel')
    .directive('matriculaTranscricaoFieldset', matriculaTranscricaoFieldset);

  function matriculaTranscricaoFieldset(
    UfsService,
    MunicipioService,
    CartorioService,
    MatriculaService,
    $mdDialog,
    PesquisarImovelMatricula,
    MensagensService,
    PaginacaoFactory,
    NotificadorService,
    NOTIFICADORES,
    ABA_DADOS_GERAIS
  ) {
    return {
      templateUrl:
        'scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/fieldset-matricula-transcricao/view/matricula-transcricao-fieldset.html',
      scope: {
        imovel: '=',
        isDetail: '=',
        action: '@',
      },
      link: function ($scope) {
        var vm = $scope,
          init,
          removaMatricula,
          ID_PAIS_BRASIL = 1,
          ID_SITUACAO_INCORPORACAO_INCORPORADO = 1,
          TIPO_IMOVEL_UNIDADE_AUTONOMA = 'UNIDADE AUTÔNOMA';

        vm.ordenacao = null;
        vm.paginacaoMatriculas = PaginacaoFactory.getDefault();

        init = function () {
          vm.ufs = UfsService.UFS;
          vm.matricula = {};
          vm.hoje = new Date();
          vm.forms = {};
        };

        vm.pesquisaMunicipio = function (uf) {
          if (uf) {
            vm.municipios = MunicipioService.ApenasMunicipios.query({ uf: uf });
          }
        };

        vm.changeUF = function (uf) {
          vm.municipios = [];
          delete vm.matricula.municipio;
          vm.cartorios = [];
          delete vm.matricula.cartorio;
          vm.pesquisaMunicipio(uf);
        };

        vm.isPaisBrasil = function () {
          return (
            vm.imovel.enderecamento.pais &&
            vm.imovel.enderecamento.pais.id === ID_PAIS_BRASIL
          );
        };

        vm.carregarCartorios = function (matricula) {
          vm.cartorios = [];
          delete vm.matricula.cartorio;
          if (
            matricula.municipio &&
            matricula.municipio.codigo &&
            matricula.municipio.uf
          ) {
            vm.cartorios = CartorioService.pesquisaCartoriosPorUF.query({
              uf: matricula.municipio.uf.sigla,
              municipio: matricula.municipio.codigo
            });
          }
        };

        vm.hasMatricula = function () {
          return vm.imovel.incorporacao.matriculas.length > 0;
        };

        vm.alteraMatricula = function (matricula) {
          vm.matricula.uf = matricula.uf;
          vm.matricula.cartorioID = matricula.cartorioID;
          preparaMunicipio(matricula.uf, matricula.municipio);
          vm.matricula.cartorioEstrangeiro = matricula.cartorioEstrangeiro;
          vm.matricula.numero = matricula.numero;
          vm.matricula.dataRegistro = toDate(matricula.dataRegistro);
          vm.matricula.index = vm.imovel.incorporacao.matriculas.indexOf(
            matricula
          );
        };

        function toDate(dateStr) {
          if (typeof dateStr != 'string') {
            return dateStr;
          }
          var parts = dateStr.split('-');
          return new Date(parts[0], parts[1] - 1, parts[2]);
        }

        var preparaMunicipio = function (uf, nome) {
          vm.pesquisaMunicipio(uf);
          vm.municipios.$promise.then(function (data) {
            for (var i = 0; i < data.length; i++) {
              if (data[i].nome === nome) {
                vm.matricula.municipio = data[i];
                preparaCartorio(vm.matricula);
                return;
              }
            }
          });
        };

        var preparaCartorio = function (matricula) {
          vm.carregarCartorios(matricula);
          vm.cartorios.$promise.then(function (data) {
            for (var i = 0; i < data.length; i++) {
              if (data[i].id == matricula.cartorioID) {
                vm.matricula.cartorio = data[i];
                return;
              }
            }
          });
        };

        vm.isTipoImovelUnidadeAutonoma = function () {
          return (
            vm.imovel &&
            vm.imovel.tipoImovel &&
            vm.imovel.tipoImovel.tipoPai === TIPO_IMOVEL_UNIDADE_AUTONOMA
          );
        };

        vm.isMatriculaObrigatoria = function () {

          var matriculaObrigatoria = true;

          switch (vm.imovel) {
          case vm.imovel.tipoImovel.id === 3:
             matriculaObrigatoria = false;
            break;
          case vm.imovel.incorporacao.tipoIncorporacao === 1:
            matriculaObrigatoria = false;
            break;
          case vm.imovel.incorporacao.situacaoIncorporacao === 2:
            matriculaObrigatoria = false;
            break;
          }

          return matriculaObrigatoria;
        };

        vm.isImovelEspelhoDagua = function () {
          return (
            vm.imovel &&
            vm.imovel.tipoImovel &&
            vm.imovel.tipoImovel.id === 3
          );
        };


        vm.adicionaMatricula = function (matricula) {
          if (!matricula) {
            return;
          }

          if(typeof matricula.uf !== 'string'){
              matricula.uf = matricula.uf.sigla;
          }

          if (vm.isPaisBrasil()) {
            if (
              !matricula.uf ||
              !matricula.municipio ||
              !matricula.cartorio ||
              vm.numeroIsNull(matricula.numero)
            ) {
              vm.forms.matriculaForm.$setSubmitted();
              MensagensService.exibirMensagemCamposObrigatorios();
              return;
            }
          } else {
            if (vm.numeroIsNull(matricula.numero)) {
              MensagensService.exibirMensagemCamposObrigatorios();
              return;
            }
          }

          if (vm.isTipoImovelUnidadeAutonoma()) {
            var _matricula = angular.copy(matricula);
            PesquisarImovelMatricula.getImovelMatricula({
              numeroMatricula: matricula.numero,
              idCartorio: matricula.cartorio.id
            }).$promise.then(function (data) {
              if (data.length > 0) {
                MensagensService.exibirMensagemErro(
                  'A Matrícula/Transcrição informada já está cadastrada em outra Unidade Autônoma. Favor informar um valor diferente. '
                );
              } else {
                vm.adicionarMatricula(_matricula);
              }
            });
          } else {
            vm.adicionarMatricula(matricula);
          }
        };

        vm.numeroIsNull = function (numero) {
          return angular.isUndefined(numero) || numero == null;
        };

        vm.adicionarMatricula = function (matricula) {
          vm.matricula = matricula;

          var _matricula = angular.copy(matricula);
          if (_matricula.municipio) {
            _matricula.municipio = _matricula.municipio.nome;
          }
          if (_matricula.cartorio) {
            _matricula.cartorioID = _matricula.cartorio.id;
          }

          if (vm.isNovaMatricula(matricula)) {
            vm.imovel.incorporacao.matriculas.push(_matricula);
          } else {
            vm.imovel.incorporacao.matriculas.splice(
              matricula.index,
              1,
              _matricula
            );
          }

          vm.resetMatricula();
        };

        vm.isSituacaoIncorporacaoIncorporado = function () {
          return (
            vm.imovel.incorporacao.situacaoIncorporacao &&
            vm.imovel.incorporacao.situacaoIncorporacao.id ===
              ID_SITUACAO_INCORPORACAO_INCORPORADO
          );
        };

        vm.isNovaMatricula = function (matricula) {
          return (
            angular.isUndefined(matricula.index) || matricula.index == null
          );
        };

        vm.resetMatricula = function () {
          vm.matricula.uf = '';
          vm.matricula.municipio = '';
          vm.matricula.cartorio = '';
          vm.matricula.cartorioEstrangeiro = '';
          vm.matricula.numero = '';
          vm.matricula.dataRegistro = null;
          vm.matricula.index = undefined;
          vm.forms.matriculaForm.$setPristine();
          vm.forms.matriculaForm.$setUntouched();
        };

        vm.confirmaRemocao = function (matricula) {
          var confirm = $mdDialog
            .confirm({ focusOnOpen: false })
            .textContent('Deseja realmente remover esta matrícula? Isso irá excluir todos os Documentos Cartoriais associados.')
            .ariaLabel('Remover')
            .cancel('Não')
            .ok('Remover');

          $mdDialog.show(confirm).then(
            function () {
              removaMatricula(matricula);
              $mdDialog.hide();
            },
            function () {
              $mdDialog.cancel();
            }
          );
        };

        removaMatricula = function (matricula) {
          for (var index = vm.imovel.incorporacao.certidoesCartoriais.length-1; index >= 0; index--) {
            if(vm.imovel.incorporacao.certidoesCartoriais[index].cartorio==matricula.cartorioID)
              vm.imovel.incorporacao.certidoesCartoriais.splice(index, 1);
          }
          var _index = vm.imovel.incorporacao.matriculas.indexOf(matricula);
          vm.imovel.incorporacao.matriculas.splice(_index, 1);
        };

        vm.isDetalhar = function () {
          return vm.action === 'detail';
        };

        $scope.$watch('imovel.enderecamento', function () {
          if (vm.imovel.enderecamento.uf != undefined) {
            vm.matricula.uf = angular.copy(vm.imovel.enderecamento.uf);
          }

          if (
            vm.imovel.enderecamento.uf != undefined &&
            vm.imovel.enderecamento.municipio != undefined
          ) {
            preparaMunicipio(
              vm.imovel.enderecamento.uf,
              vm.imovel.enderecamento.municipio
            );
          }

          if (!vm.isPaisBrasil()) {
            vm.matricula.cidadeExterior = angular.copy(
              vm.imovel.enderecamento.cidadeExterior
            );
          }
        });

        NotificadorService.subscribe(vm, function(evento, escopo, tabIndex) {
          if (tabIndex == ABA_DADOS_GERAIS){
            vm.matricula = {}
          }
        }, NOTIFICADORES.LIMPAR_ABA);

        init();
      }
    };
  }
})();

(function () {
  'use strict';
  DadosAutarquiaController.$inject = ["$scope", "buscarCpfCnpjService", "MensagensService"];
  angular.module('su-cadastro-imovel').controller('DadosAutarquiaController', DadosAutarquiaController);
  function DadosAutarquiaController($scope, buscarCpfCnpjService, MensagensService) {

      var vm = $scope;

      vm.inputUgCnpj = '';

      if(vm.proprietario.ugAutarquia != '')
        vm.mostraAutarquia = true;
      else
        vm.mostraAutarquia = false;

      vm.validarUg= function (ugCnpj) {
          if (ugCnpj != null) {
              ugCnpj = ugCnpj.replace(/[^A-Z0-9]/g, '');
              ugCnpj = ugCnpj.replace(/^\s+|\s+$/g, '');
              vm.buscarAutarquia(ugCnpj)
          }
      };

      vm.buscarAutarquia = function (ugCnpj) {
          buscarCpfCnpjService.buscarAutarquia(ugCnpj).then(function (response) {
              if(response.data.nome == undefined){
                  MensagensService.exibirMensagemErro("UG/CNPJ não encontrado");
              }else{
                vm.nome = response.data.nome;
                vm.ug = response.data.codigo;
                vm.cnpj = response.data.cnpj;
                vm.preencheInfoProprietario(vm.nome, vm.ug, vm.cnpj);

                vm.mostraAutarquia = true;

                vm.validarMesmaUG();

              }
          }, function errorCallback() {

              MensagensService.exibirMensagemErro("UG/CNPJ não encontrado");
          })

      };

      vm.preencheInfoProprietario = function(pNome, pUg, pCnpj){
        if(vm.isLocatario == true){
          vm.proprietario.nomeLocatario = pNome;
          vm.proprietario.ugLocatario = pUg;
          vm.proprietario.cnpjLocatario = pCnpj;
        }else{
          vm.proprietario.nomeAutarquia = pNome;
          vm.proprietario.ugAutarquia = pUg;
          vm.proprietario.cnpjAutarquia = pCnpj;
        }

      };

      vm.validarMesmaUG = function(){
        if (vm.imovel.proprietario!=null
            && vm.imovel.proprietarioAnterior!=null
            && vm.imovel.proprietario.ugAutarquia == vm.imovel.proprietarioAnterior.ugAutarquia
        ) {
          MensagensService.exibirMensagemErro("O proprietário anterior não pode ser o mesmo proprietário do imóvel informado");
          vm.nome = '';
          vm.ug = '';
          vm.cnpj = '';
          vm.preencheInfoProprietario(vm.nome, vm.ug, vm.cnpj);
        }

      }



  }
})();

(function () {
  'use strict';
  directive.$inject = ["TipoBenfeitoriaService", "TipoProjetoService", "TipoEspecializacaoEdificacaoService", "TipoEspecializacaoComplementarService", "DominioBenfeitoria", "TipoEstruturaService", "PadraoAcessibilidade", "NotificadorService", "MAPA_CONSTANTS_INCORPORACAO", "ArquivoService", "$mdDialog", "MensagensService", "PaginacaoFactory", "UtilizacoesService", "MigracaoBenfeitoria", "ExibirPlantaBenfeitoria", "ABA_BENFEITORIA"];
  angular.module('su-cadastro-imovel').directive('inclusaoBenfeitoria', directive);

  function directive(TipoBenfeitoriaService, TipoProjetoService, TipoEspecializacaoEdificacaoService,
       TipoEspecializacaoComplementarService, DominioBenfeitoria, TipoEstruturaService, PadraoAcessibilidade,
        NotificadorService, MAPA_CONSTANTS_INCORPORACAO, ArquivoService, $mdDialog, MensagensService,
         PaginacaoFactory, UtilizacoesService, MigracaoBenfeitoria, ExibirPlantaBenfeitoria,  ABA_BENFEITORIA) {

    return {
        templateUrl: 'scripts-cadastro-imovel/app/directives/secoes-formulario/benfeitoria/inclusao-benfeitoria/inclusao-benfeitoria.html',
        scope: {
            benfeitoria: '=',
            imovel: '=',
            showInclusaoBenfeitoria: '=',
            showTabFormDocumento: '=',
            documento: '=',
            isDetail: '=',
            action: '@'
          },
        controller: ["$scope", function ($scope) {
            var vm = this, scope = $scope;

            vm.tipoDesenhoBenfeitoria = undefined;
            vm.fotoFile = undefined;
            vm.plantaFile = undefined;
            vm.showIntegracaoSPIU = false;
            vm.showInclusaoBenfeitoria = scope.showInclusaoBenfeitoria;
            vm.showTabFormDocumento = scope.showTabFormDocumento;
            vm.exibeTabFormDocumento = function (isExibeTabFormDocumento, isExibeTabInclusaoBenfeitoria, documento) {

                scope.showInclusaoBenfeitoria = isExibeTabInclusaoBenfeitoria;
                scope.showTabFormDocumento = isExibeTabFormDocumento;
                if (documento) {
                    if (!isEmpty(documento.dataEmissao)) {
                        documento.dataEmissao = toDate(documento.dataEmissao);
                      }

                    if (!isEmpty(documento.dataValidade)) {
                        documento.dataValidade = toDate(documento.dataValidade);
                      }
                    scope.documento = angular.copy(documento);
                    scope.documento.index = scope.benfeitoria.edificacao.documentacao.indexOf(documento);
                    scope.documentoBackup = documento;
                  }
              };

            function toDate(dateStr) {
                if(typeof dateStr != 'string'){
                    return dateStr;
                  }
                var parts = dateStr.split('/');
                var date = parts[2] + '-' + parts[1] + '-' + parts[0] + ' 00:00';
                return new Date(date);
              }

            function isEmpty(value) {
                return angular.equals({}, value) || angular.isUndefined(value) || value === null || value === '';
              }


          }],
        controllerAs: 'ctrl',
        link: function ($scope, el, attrs, ctrl) {
            var vm = $scope,
                notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES,
                featureIds = MAPA_CONSTANTS_INCORPORACAO.FEATURE_ID;

            vm.benfeitoriaBackup = angular.copy(vm.benfeitoria);
            vm.benfeitorias = [];
            vm.tiposBenfeitoria = TipoBenfeitoriaService.query();
            vm.tiposEspecializacaoEdificacao = TipoEspecializacaoEdificacaoService.query();
            vm.tiposProjeto = TipoProjetoService.query();
            vm.tiposEspecializacaoComplementar = TipoEspecializacaoComplementarService.query();
            vm.tiposEnce = DominioBenfeitoria.tipoEnce.query();
            vm.tiposEnceParcial = DominioBenfeitoria.tipoEnceParcial.query();
            vm.niveisEnce = DominioBenfeitoria.nivelEnce.query();
            vm.tiposEstrutura = TipoEstruturaService.query();
            vm.constantesAcessibilidade = PadraoAcessibilidade.query();
            vm.fotoBenfeitoria = {};
            vm.filesBenfeitoria = {
                shapefile: {}
              };
            vm.arquivosDocumentacaoBenfeitoria = {
                planoProtecaoIncendioFile: undefined,
                habiteseFile: undefined,
                alvaraConstrucaoFile: undefined,
                laudoInspecaoFile: undefined,
                relatorioAcessibilidadeFile: undefined,
                outroDocumento: {
                    descricao: undefined,
                    arquivo: undefined
                  }
              };
            vm.objeto = null;
            vm.exibirBotao = true;
            vm.plantaBenfeitoria = {};
            vm.ordenacaoPlantas = null;
            vm.ordenacaoFotos = null;
            vm.paginacaoPlantas = PaginacaoFactory.getDefault();
            vm.paginacaoFotos = PaginacaoFactory.getDefault();
            vm.geometriaValida = false;

            vm.showConfirmaInclusao = false;
            vm.showMsgZeroUtilizacoes = false;

            vm.tabelaOutrosDocumentos = {
                limit: 5,
                page: 1,
                limitOptions: [5, 10, 15]
              };

            function init() {
                vm.geometriaValida = !!vm.benfeitoria && vm.isBenfeitoriaEdificacao() && !!vm.benfeitoria.geometriaBenfeitoria;
                gerarUrlImagens();
                gerarUrlPlantas();
                if (vm.benfeitoria.arquivoGeometria) {
                    ctrl.tipoDesenhoBenfeitoria = 1;
                  } else {
                    ctrl.tipoDesenhoBenfeitoria = 2;
                  }

                MigracaoBenfeitoria.liberada().then(function(response){
                      //console.log(response)
                    if(response == 'TRUE'){
                        if($scope.imovel.id !== null && angular.isDefined($scope.imovel.id)){

                          UtilizacoesService.query({idImovel: $scope.imovel.id}, function (retorno){
                            vm.utilizacoesSpiu = retorno;
                            vm.showIntegracaoSPIU = true;

                            if(!vm.benfeitoria.utilizacoes) {
                                vm.benfeitoria.utilizacoes = [];
                            }

                            if(vm.utilizacoesSpiu && vm.utilizacoesSpiu.length === 0)
                              vm.showMsgZeroUtilizacoes = true;
                          });

                          //$scope.tableData = vm.imovel.benfeitorias;

                        }
                        //vm.utilizacaoOriginal = vm.benfeitoria.ripUtilizacaoSpiu;
                      }
                  });

              }

            NotificadorService.subscribe($scope, function (evento, escopo, data) {
                vm.benfeitoria.geometriaBenfeitoria = data.geoBenfeitoria;
                vm.geometriaValida = data.isValid;
              }, notifiers.GEOMETRIA_BENFEITORIA_ALTERADA);

            $scope.$watch(
                    function () {
                      return vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao;
                    },
                    /**
                     * @param {object} newValue - Novo valor para o atributo tipoEspecializacaoEdificacao.
                     */
                    function (newValue) {
                      if (newValue && newValue.id === 8) {
                        vm.benfeitoria.edificacao.numeroPavimentos = 1;
                      }
                    }
                );

            vm.utilizacaoSelecionada = function () {
                vm.showConfirmaInclusao = true;
              };

            vm.benfeitoriaContemUtilizacao = function (utilizacao) {
                if (vm.benfeitoria.utilizacoes)
                    return vm.benfeitoria.utilizacoes.some(function(e) { return e.ripUtilizacao==utilizacao;} );
              };

            vm.atualizaUtilizacoesBenfeitoria = function () {
                if(vm.showConfirmaInclusao && vm.benfeitoria.utilizacoes) {
                    for (var i = vm.benfeitoria.utilizacoes.length-1; i >= 0 ; i--) {
                        if (!vm.benfeitoria.ripUtilizacaoSpiu.includes(vm.benfeitoria.utilizacoes[i].ripUtilizacao.toString())) {
                            vm.benfeitoria.utilizacoes.splice(i,1);
                          }
                      }
                    for (var j = vm.benfeitoria.ripUtilizacaoSpiu.length-1; j >= 0 ; j--) {
                        if (!vm.benfeitoria.utilizacoes.some(function(e) { return e.ripUtilizacao==vm.benfeitoria.ripUtilizacaoSpiu[j];} )) {
                            vm.benfeitoria.utilizacoes.push({
                                id: null,
                                ripUtilizacao: vm.benfeitoria.ripUtilizacaoSpiu[j],
                                area: 0,
                                fracao: 0
                              });
                          }
                      }
                  }
              };

                /*vm.chgBenfeitoriaImovelSpiu = function () {
                  vm.showConfirmaInclusao = true;
                  if(vm.benfeitoria.benfeitoriaImovelSpiu){
                    vm.benfeitoria.ripUtilizacaoSpiu = "";
                  }else{
                    if(vm.utilizacaoOriginal)
                      vm.benfeitoria.ripUtilizacaoSpiu = vm.utilizacaoOriginal;
                  }
                };*/

            vm.cancelarInclusaoBenfeitoria = function () {
                NotificadorService.notify($scope, {
                    codigo: vm.benfeitoriaBackup.codigo,
                    geometriaBenfeitoria: vm.benfeitoriaBackup.geometriaBenfeitoria
                  }, notifiers.DESFAZER_ALTERACAO_BENFEITORIA);
                vm.showInclusaoBenfeitoria = false;
                vm.limparInclusaoBenfeitoria();
              };

            vm.atualizarLinkLaudo = function () {
                if (vm.benfeitoria.edificacao.possuiLaudo.id !== 2) {
                    vm.benfeitoria.edificacao.linkLaudo = null;
                  }
              };

            vm.atualizarLinkPlano = function () {
                if (vm.benfeitoria.edificacao.possuiPlanoTrabalho.id !== 2) {
                     vm.benfeitoria.edificacao.linkPlanoTrabalho = null;
                   }
              };


            vm.updateInformacoesAcessibilidade = function() {

                if (vm.benfeitoria.edificacao.semInformacaoAcessibilidade) {

                     vm.benfeitoria.edificacao.possuiLaudo ? vm.benfeitoria.edificacao.possuiLaudo.id = null: null;
                     vm.benfeitoria.edificacao.linkLaudo = null;
                     vm.benfeitoria.edificacao.possuiPlanoTrabalho.id = null;
                     vm.benfeitoria.edificacao.linkPlanoTrabalho = null;
                     vm.benfeitoria.edificacao.vagasDefFisicos  = null;
                     vm.benfeitoria.edificacao.vagasIdosos = vm.constantesAcessibilidade.id = null;
                     vm.benfeitoria.edificacao.calcadasRebaixa = null;
                     vm.benfeitoria.edificacao.calcadasLivres = null;
                     vm.benfeitoria.edificacao.entradaLivre = null;
                     vm.benfeitoria.edificacao.pavimentosLivreCadRodas = null;
                     vm.benfeitoria.edificacao.balcaoInformacoes = null;
                     vm.benfeitoria.edificacao.pisoTatil = null;
                     vm.benfeitoria.edificacao.corredorLargo = null;
                     vm.benfeitoria.edificacao.sanitarioPublico = null;
                     vm.benfeitoria.edificacao.braile = null;
                     vm.benfeitoria.edificacao.libras = null;
                   }

              };

                /**
                 * @property {object} inclusaoBenfeitoriaForm - Formulário de inclusão de benfeitoria.
                 */
            vm.limparInclusaoBenfeitoria = function () {

                vm.benfeitoria = {
                    tipoBenfeitoria: {},
                    edificacao: {
                        documentacao: [ {nome: 'Plano de Proteção Contra Incêndio', possui: 'SEM_INFORMACAO', isPrecadastrado: true},
                                {nome: 'Habite-se', possui: 'SEM_INFORMACAO', isPrecadastrado: true},
                                {nome: 'Alvará de Construção/Reforma', possui: 'SEM_INFORMACAO', isPrecadastrado: true},
                                {nome: 'Laudo de Inspeção Predial', possui: 'SEM_INFORMACAO', isPrecadastrado: true},
                                {nome: 'Relatório de Acessibilidade', possui: 'SEM_INFORMACAO', isPrecadastrado: true}],
                        dadosMedidor: []
                      },
                    imagens: [],
                    plantas: []
                  };
                vm.benfeitoriaBackup = {};
                vm.fotoBenfeitoria = {};
                ctrl.fotoFile = undefined;
                vm.limparPlantaBenfeitoriaForm();
                vm.forms.inclusaoBenfeitoriaForm.$setUntouched();
                vm.forms.inclusaoBenfeitoriaForm.$setPristine();
                vm.tipoDesenhoBenfeitoria = undefined;
                vm.geometriaValida = false;

                    //para limpar o desenho
                    //NotificadorService.notify($scope, ABA_BENFEITORIA, notifiers.LIMPAR_ABA);
              };

                /**
                 * @property {object} plantaBenfeitoriaForm - Formulário de plantas de benfeitoria.
                 */
            vm.limparPlantaBenfeitoriaForm = function () {
                vm.plantaBenfeitoria = {};
                ctrl.plantaFile = undefined;
                vm.submittedBenfeitoria = false;
                vm.forms.plantaBenfeitoriaForm.$setUntouched();
                vm.forms.plantaBenfeitoriaForm.$setPristine();
              };

            vm.salvarBenfeitoria = function () {

                if (vm.forms.inclusaoBenfeitoriaForm.$valid && isGeometriaValid()) {

                    if(isNumeroPavimentoMaiorQueUm()) {
                      var codigoOriginal = undefined;
                      if (vm.benfeitoria.codigo) {
                          codigoOriginal = vm.benfeitoria.codigo;
                          angular.forEach(vm.imovel.benfeitorias, function (benfeitoria) {
                                if (benfeitoria.codigo === codigoOriginal) {
                                    excluirBenfeitoria(benfeitoria);
                                  }
                              });
                        }
                      vm.benfeitoria.codigo = gerarCodigoBenfeitoria();
                      vm.benfeitoria.ativa = true;
                      vm.imovel.benfeitorias.push(vm.benfeitoria);

                      if (vm.isBenfeitoriaEdificacao()) {
                          NotificadorService.notify($scope, {
                                oldValue: codigoOriginal || featureIds.ACESSAO_BENFEITORIA,
                                newValue: vm.benfeitoria.codigo
                              }, notifiers.SET_ID_BENFEITORIA);
                        }

                      vm.limparInclusaoBenfeitoria();
                      vm.showInclusaoBenfeitoria = false;
                      MensagensService.exibirMensagemSucesso('Benfeitoria incluída com sucesso. Não esqueça de gravar o imóvel.');
                      NotificadorService.notify(vm, {}, notifiers.CARREGAR_UTILIZACOES_BENFEITORIAS);
                    }
                  } else if (vm.forms.inclusaoBenfeitoriaForm.$error.required) {
                    vm.forms.inclusaoBenfeitoriaForm.$setSubmitted();
                    vm.submittedBenfeitoria = true;
                    MensagensService.exibirMensagemCamposObrigatorios();
                  } else if (!isGeometriaValid()) {
                      MensagensService.exibirMensagemErro('Necessário informar uma geometria válida');
                    }

              };

            function isNumeroPavimentoMaiorQueUm () {
                if(isMultipavimentos() && vm.benfeitoria.edificacao.numeroPavimentos < 2) {
                    MensagensService.exibirMensagemErro('O número de pavimentos deve ser maior que 1');
                    return false;
                  }
                return true;
              }

            vm.confirmarExclusaoBenfeitoria = function(benfeitoria, ev){
                var confirm = $mdDialog.confirm({
                    focusOnOpen: false
                  })
                        .textContent('Deseja realmente excluir esta acessão/benfeitoria?')
                        .targetEvent(ev)
                        .cancel('Não')
                        .ok('Sim');

                $mdDialog.show(confirm).then(function(){
                    excluirBenfeitoria(benfeitoria);
                    NotificadorService.notify($scope, benfeitoria.codigo, notifiers.BENFEITORIA_EXCLUIDA);
                  });
              };

                //Confirmar inclusão de benfeitoria

            vm.confirmarInclusaoBenfeitoria = function(ev) {
                if(vm.showConfirmaInclusao){
                    $mdDialog.show({
                      controller: confirmarBenfeitoriaController,
                      templateUrl: 'scripts-cadastro-imovel/pages/benfeitoria/view/dialog-confirmar-edicao-benfeitoria.html',
                      parent: angular.element(document.body),
                      targetEvent: ev,
                      clickOutsideToClose: true
                    });
                  } else {
                    vm.salvarBenfeitoria();
                  }
              };

            var confirmarBenfeitoriaController = function($mdDialog, $scope) {

                $scope.fechar = function() {
                  $mdDialog.cancel();
                };

                $scope.salvar = function(){
                  vm.salvarBenfeitoria();
                  $mdDialog.hide();
                };

              };
              confirmarBenfeitoriaController.$inject = ["$mdDialog", "$scope"];






            function excluirBenfeitoria(benfeitoria) {
                var index = vm.imovel.benfeitorias.indexOf(benfeitoria);
                vm.imovel.benfeitorias.splice(index, 1);
              }

            vm.adicionarFotoBenfeitoria = function () {
                if (vm.fotoBenfeitoria.descricao && vm.fotoBenfeitoria.dataEmissao && ctrl.fotoFile) {

                    if (!isImagemFormatoValido()) {
                        MensagensService.exibirMensagemErro('Arquivo em formato inválido');
                      } else if (!isImagemTamanhoValido()) {
                          MensagensService.exibirMensagemErro('O arquivo não pode possuir mais de 5mb');
                        } else {
                          ArquivoService.uploadArquivo(ctrl.fotoFile).then(
                                function (response) {
                                  vm.fotoBenfeitoria.imagem = response.data.byteArray;
                                  vm.fotoBenfeitoria.nomeOriginal = response.data.nome;
                                  vm.benfeitoria.imagens.push(vm.fotoBenfeitoria);
                                  limparDadosFotos();
                                }
                            );
                        }

                  } else {
                    vm.forms.imagemBenfeitoriaForm.$setSubmitted();
                    MensagensService.exibirMensagemCamposObrigatorios();
                  }
              };

            vm.excluirFotoBenfeitoria = function (ev, foto) {
                var confirm = $mdDialog.confirm({
                    focusOnOpen: false
                  })
                        .textContent('Deseja confirmar a exclusão da foto?')
                        .targetEvent(ev)
                        .cancel('Não')
                        .ok('Sim');

                $mdDialog.show(confirm).then(
                        function () {
                          var index = vm.benfeitoria.imagens.indexOf(foto);
                          vm.benfeitoria.imagens.splice(index, 1);
                        }
                    );
              };

            vm.adicionarPlantaBenfeitoria = function () {
                if (vm.plantaBenfeitoria.nome && vm.plantaBenfeitoria.data && ctrl.plantaFile) {
                    ArquivoService.uploadArquivo(ctrl.plantaFile).then(
                            function (response) {
                              vm.plantaBenfeitoria.planta = response.data.byteArray;
                              vm.plantaBenfeitoria.nomeOriginal = response.data.nome;
                              vm.plantaBenfeitoria.url = ArquivoService.getUrlFromBase64(vm.plantaBenfeitoria.planta);
                              vm.benfeitoria.plantas.push(vm.plantaBenfeitoria);
                              vm.limparPlantaBenfeitoriaForm();
                            }
                        );
                  } else {
                    vm.forms.plantaBenfeitoriaForm.$setSubmitted();
                    MensagensService.exibirMensagemCamposObrigatorios();
                  }
              };

            vm.excluirPlantaBenfeitoria = function (ev, planta) {
                var confirm = $mdDialog.confirm({
                    focusOnOpen: false
                  })
                        .textContent('Tem certeza que deseja excluir este arquivo?')
                        .targetEvent(ev)
                        .cancel('Não')
                        .ok('Sim');

                $mdDialog.show(confirm).then(
                        function () {
                          var index = vm.benfeitoria.plantas.indexOf(planta);
                          vm.benfeitoria.plantas.splice(index, 1);
                        }
                    );
              };

            vm.uploadShapefileBenfeitoria = function () {
                ArquivoService.uploadShapefile(vm.filesBenfeitoria.shapefile, vm.imovel.enderecamento.codMunicipio).then(
                        function (response) {
                          vm.arquivoShape = response.data;
                          vm.benfeitoria.arquivoGeometria = {};
                          vm.benfeitoria.arquivoGeometria.arquivo = response.data.byteArray;
                          vm.benfeitoria.arquivoGeometria.nomeOriginal = response.data.nome;
                          vm.benfeitoria.arquivoGeometria.tipoArquivo = response.data.tipo;
                          vm.benfeitoria.geometriaBenfeitoria = response.data.geometria;
                          vm.geometriaValida = true;
                          NotificadorService.notify($scope, {id: vm.benfeitoria.codigo || featureIds.ACESSAO_BENFEITORIA, geometria: response.data.geometria}, notifiers.UPLOAD_SHAPEFILE_BENFEITORIA);
                        },
                        function errorCallback(error) {
                          vm.filesBenfeitoria.shapefile = undefined;
                          MensagensService.exibirMensagemErro(error.data.message);
                        }
                    );
              };

            vm.limparUploadShapefileBenfeitoria = function () {
                vm.benfeitoria.geometriaBenfeitoria = undefined;
                NotificadorService.notify($scope, vm.benfeitoria.codigo || featureIds.ACESSAO_BENFEITORIA, notifiers.LIMPAR_DESENHO);
              };

            vm.getIndiceAproveitamento = function () {
                return dividirPelaAreaTotalTerreno(vm.benfeitoria.areaConstruida, vm.benfeitoria.edificacao.indiceAproveitamento);
              };

            vm.getTaxaOcupacao = function () {
                if (!vm.benfeitoria.edificacao) {
                    vm.benfeitoria.edificacao = {};
                  }

                return dividirPelaAreaTotalTerreno(vm.benfeitoria.edificacao.areaProjecao, vm.benfeitoria.edificacao.taxaOcupacao);
              };

            vm.isBenfeitoriaComplementar = function () {
                if (vm.benfeitoria.tipoBenfeitoria && vm.benfeitoria.tipoBenfeitoria.id === 2) {
                    ctrl.tipoDesenhoBenfeitoria = undefined;
                    return true;
                  }
                return false;
              };

            vm.isBenfeitoriaEdificacao = function () {
                return vm.benfeitoria.tipoBenfeitoria && vm.benfeitoria.tipoBenfeitoria.id === 1;
              };

                // vm.getNumberMaskAreaConstruida = function () {
                //     return vm.isBenfeitoriaEdificacao() ? 2 : 4;
                // };

            vm.isBenfeitoriaComGaragem = function () {
                return vm.benfeitoria.edificacao.possuiGaragem === 'Sim';
              };

            vm.isNumeroPavimentosDisabled = function () {
                return vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao && vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 8;
              };

            function isMultipavimentos() {
                return vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao && vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 9;
              }

            vm.isNumeroPavimentosRequired = function () {
                return vm.isBenfeitoriaEdificacao() && vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao && vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id !== 10;
              };

            vm.uploadArquivoPlanoProtecaoIncendio = function () {
                if (!vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoPlanoProtecaoIncendio) {
                    vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoPlanoProtecaoIncendio = {};
                  }
                setDocumentacaoEdificacao(vm.arquivosDocumentacaoBenfeitoria.planoProtecaoIncendioFile, vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoPlanoProtecaoIncendio);
              };

            vm.uploadArquivoHabitese = function () {
                if (!vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoHabitese) {
                    vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoHabitese = {};
                  }
                setDocumentacaoEdificacao(vm.arquivosDocumentacaoBenfeitoria.habiteseFile, vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoHabitese);
              };

            vm.uploadArquivoAlvaraConstrucao = function () {
                if (!vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoAlvaraConstrucao) {
                    vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoAlvaraConstrucao = {};
                  }
                setDocumentacaoEdificacao(vm.arquivosDocumentacaoBenfeitoria.alvaraConstrucaoFile, vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoAlvaraConstrucao);
              };

            vm.uploadArquivoLaudoInspecao = function () {
                if (!vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoLaudoInspecao) {
                    vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoLaudoInspecao = {};
                  }
                setDocumentacaoEdificacao(vm.arquivosDocumentacaoBenfeitoria.laudoInspecaoFile, vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoLaudoInspecao);
              };

            vm.uploadArquivoRelatorioAcessibilidade = function () {
                if (!vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoRelatorioAcessibilidade) {
                    vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoRelatorioAcessibilidade = {};
                  }
                setDocumentacaoEdificacao(vm.arquivosDocumentacaoBenfeitoria.relatorioAcessibilidadeFile, vm.benfeitoria.edificacao.documentacaoEdificacao.arquivoRelatorioAcessibilidade);
              };

            vm.adicionarOutroDocumento = function () {
                ArquivoService.uploadArquivo(vm.arquivosDocumentacaoBenfeitoria.outroDocumento.arquivo).then(
                        function (response) {
                          var documento = {
                            descricao: vm.arquivosDocumentacaoBenfeitoria.outroDocumento.descricao,
                            nomeOriginal: response.data.nome,
                            tipoArquivo: response.data.tipo,
                            documento: response.data.byteArray
                          };

                          vm.benfeitoria.edificacao.documentacaoEdificacao.outrosDocumentos.push(documento);
                          vm.arquivosDocumentacaoBenfeitoria.outroDocumento = {
                            descricao: undefined,
                            arquivo: undefined
                          };
                        }
                    );
              };

            vm.excluirOutroDocumento = function (documento) {
                var index = vm.benfeitoria.edificacao.documentacaoEdificacao.outrosDocumentos.indexOf(documento);
                vm.benfeitoria.edificacao.documentacaoEdificacao.outrosDocumentos.splice(index, 1);
              };

            vm.alterarTipoDesenho = function() {
                if (ctrl.tipoDesenhoBenfeitoria && ctrl.tipoDesenhoBenfeitoria === 2) {
                    NotificadorService.notify($scope, null, 'bloquearTelaBenfeitoria');
                  }
              };

            vm.validarAreaProjecao = function () {

                if (vm.imovel.terreno.areaTotalCadastral > 0 && vm.imovel.terreno.areaTotalCadastral < vm.benfeitoria.edificacao.areaProjecao) {
                    vm.benfeitoria.edificacao.areaProjecao = null;
                    MensagensService.exibirMensagemErro('A área de projeção não pode ser superior à área do terreno do imóvel.');
                  }

                if (vm.imovel.terreno.areaShapeFileCartorial > 0 && vm.imovel.terreno.areaShapeFileCartorial < vm.benfeitoria.edificacao.areaProjecao) {
                    vm.benfeitoria.edificacao.areaProjecao = null;
                    MensagensService.exibirMensagemErro('A área de projeção não pode ser superior à área do terreno do imóvel.');
                  }
              };

            function setDocumentacaoEdificacao(file, model) {
                ArquivoService.uploadArquivo(file).then(
                        function (response) {
                          model.arquivo = response.data.byteArray;
                          model.nomeOriginal = response.data.nome;
                          model.tipoArquivo = response.data.tipo;
                        }
                    );
              }

            function dividirPelaAreaTotalTerreno(valor, valorBanco) {
                if (valor) {
                    if (vm.imovel.terreno.geometriaTerrenoCartorial) {

                        if(vm.imovel.terreno.areaTotalCartorial == 0){
                            if(valorBanco) return valorBanco;
                            return 0;
                          }
                        return valor / vm.imovel.terreno.areaTotalCartorial;
                      } else if (vm.imovel.terreno.geometriaTerreno) {

                          if(vm.imovel.terreno.areaTotalCadastral == 0){
                            if(valorBanco) return valorBanco;
                            return 0;
                          }

                          return valor / vm.imovel.terreno.areaTotalCadastral;
                        } else {
                          return 0;
                        }
                  } else {
                    return 0;
                  }
              }

            function gerarCodigoBenfeitoria() {
                var prefixo = vm.isBenfeitoriaEdificacao() ? 'E' : 'C';
                var arrayCtrl = [];
                var count = 1;
                for (var i = 0; i < vm.imovel.benfeitorias.length; i++) {
                    if (vm.imovel.benfeitorias[i].codigo.indexOf(prefixo) === 0) {
                            //&& vm.imovel.benfeitorias[i].ativa
                        arrayCtrl.push(parseInt(vm.imovel.benfeitorias[i].codigo.substr(1)));
                      }
                  }
                while (arrayCtrl.includes(count)) {
                    count++;
                  }
                return prefixo + count;
              }

            function isImagemFormatoValido () {
                var formatosValidos = ['image/png', 'image/jpeg', 'image/bmp'];
                return formatosValidos.includes(ctrl.fotoFile.type);
              }

            function isImagemTamanhoValido () {
                return ctrl.fotoFile.size <= 5000000;
              }

            function limparDadosFotos() {
                vm.fotoBenfeitoria = {};
                ctrl.fotoFile = undefined;
                vm.forms.imagemBenfeitoriaForm.$setUntouched();
                vm.forms.imagemBenfeitoriaForm.$setPristine();
              }

            function isGeometriaValid() {
                return !vm.isBenfeitoriaEdificacao() || (vm.benfeitoria.geometriaBenfeitoria && vm.geometriaValida);
              }

            vm.isDetalhar = function () {
                return vm.action === 'detail';
              };

            function gerarUrlImagens() {
                angular.forEach(vm.benfeitoria.imagens, function (arquivo) {
                    if (arquivo.imagem) {
                      arquivo.url = ArquivoService.getUrlFromBase64(arquivo.imagem);
                    }
                  });
              }

            function gerarUrlPlantas() {
                angular.forEach(vm.benfeitoria.plantas, function (planta) {
                    if(angular.isUndefined(planta.planta) || planta.planta === null ){
                        ExibirPlantaBenfeitoria.obterPlantaBenfeitoria(planta.id).then(
                              function (result) {
                                planta.planta = result.data;
                              },
                              function (error) {
                                MensagensService.exibirMensagemErro('Ocorreu um erro ao buscar a planta da benfeitoria.');
                              }
                            );
                      }
                    planta.url = ArquivoService.getUrlFromBase64(planta.planta);
                  });
              }

            init();
          }
      };
  }
})();

(function(){

	MapaOriginalController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaOriginalController", MapaOriginalController);

	function MapaOriginalController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					if(vm.imovel.rip !== null) {
						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						// var styleTerreno;
                        //
		                // var estiloPonto = new ol.style.Style({
		        		// 	image : new ol.style.Icon({
		    			// 		anchor: [0.5, 32],
				        //         anchorXUnits: 'fraction',
				        //         anchorYUnits: 'pixels',
				        //         src: 'assets/img/icons/pointer.png',
				        //         zIndex: 9999
		        		// 	})
		        		// });

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});

						if(terreno != undefined){
								var terrenoLayer = new ol.layer.Vector({
									source : new ol.source.Vector({
										features : [
											new ol.Feature({
												geometry : new ol.geom.Polygon(terreno.coordinates)
											})
										]
									})
								});
						}

						if(terrenoCartorial != undefined){
							var terrenoCartorialLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
										})
									]
								})
							});
						}

						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }



						 $scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#mapaOriginalOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});

						if(terreno != undefined){
								 map.addLayer(terrenoLayer);
						}
						if(terrenoCartorial != undefined){
							map.addLayer(terrenoCartorialLayer);
						}
						map.addLayer(benfeitoriasLayer);
						map.addLayer(unidadeAutonomaLayer);
						map.addLayer(pontoLayer);

									if(terreno != undefined){
										var terrenoExtent = terrenoLayer.getSource().getExtent();
													map.getView().fit(terrenoExtent,map.getSize());

													view.setCenter(map.getView().getCenter());
										}else{
											var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
														map.getView().fit(terrenoExtent,map.getSize());

														view.setCenter(map.getView().getCenter());
										}
	               }else{

	               		$("#mapaPontoImovel").css('display','none');

	               }


				});
			}
		});

	}



})();

(function(){

	MapaOriginalUnidadeAutonomaController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaOriginalUnidadeAutonomaController", MapaOriginalUnidadeAutonomaController);

	function MapaOriginalUnidadeAutonomaController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					if(vm.imovel.rip !== null) {
						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						var styleTerreno;

		                var estiloPonto = new ol.style.Style({
		        			image : new ol.style.Icon({
		    					anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer.png',
				                zIndex: 9999
		        			})
		        		});

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});


						if(terreno != undefined){
								var terrenoLayer = new ol.layer.Vector({
									source : new ol.source.Vector({
										features : [
											new ol.Feature({
												geometry : new ol.geom.Polygon(terreno.coordinates)
											})
										]
									})
								});
						}


						if(terrenoCartorial != undefined){
								var terrenoCartorialLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
										features : [
											new ol.Feature({
												geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
											})
										]
									})
								});
							}


						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }



						$scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#unidadeAutonomaOriginalOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});

						if(terreno != undefined){
	 						map.addLayer(terrenoLayer);
	 					}
	 					if(terrenoCartorial != undefined){
	 							map.addLayer(terrenoCartorialLayer);
	 					}
						map.addLayer(benfeitoriasLayer);
						map.addLayer(unidadeAutonomaLayer);
						map.addLayer(pontoLayer);
									if(terreno != undefined){
										var terrenoExtent = terrenoLayer.getSource().getExtent();
					            		map.getView().fit(terrenoExtent,map.getSize());

					            		view.setCenter(map.getView().getCenter());
										}else{
											var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
														map.getView().fit(terrenoExtent,map.getSize());

														view.setCenter(map.getView().getCenter());
										}
	               }else{

	               	    $(".mapaUnidadeAutonoma").css('display','none');

				   }


				});
			}
		});

	}



})();

(function(){

	MapaOriginalTerrenoController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaOriginalTerrenoController", MapaOriginalTerrenoController);

	function MapaOriginalTerrenoController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerreno;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					if(vm.imovel.rip !== null) {
						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						var styleTerreno;

		                var estiloPonto = new ol.style.Style({
		        			image : new ol.style.Icon({
		    					anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer.png',
				                zIndex: 9999
		        			})
		        		});

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});

							if(terreno != undefined){
									var terrenoLayer = new ol.layer.Vector({
										source : new ol.source.Vector({
											features : [
												new ol.Feature({
													geometry : new ol.geom.Polygon(terreno.coordinates)
												})
											]
										})
									});
							}

						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }

						$scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#terrenoOriginalOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});


						if(terreno != undefined){
							map.addLayer(terrenoLayer);
						}
						map.addLayer(benfeitoriasLayer);
						map.addLayer(unidadeAutonomaLayer);
						map.addLayer(pontoLayer);

									if(terreno != undefined){
										var terrenoExtent = terrenoLayer.getSource().getExtent();
													map.getView().fit(terrenoExtent,map.getSize());

													view.setCenter(map.getView().getCenter());
										}
	               }else{

	               		$(".mapaTerrenoImovel").css('display','none');
	            	    $(".mapaPontoImovel").css('display','none');
	               }


				});
			}
		});

	}



})();

(function(){

	MapaOriginalTerrenoCartorialController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaOriginalTerrenoCartorialController", MapaOriginalTerrenoCartorialController);

	function MapaOriginalTerrenoCartorialController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					if(vm.imovel.rip !== null) {
						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						var styleTerreno;

		                var estiloPonto = new ol.style.Style({
		        			image : new ol.style.Icon({
		    					anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer.png',
				                zIndex: 9999
		        			})
		        		});

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});

						if(terreno != undefined){
							var terrenoLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terreno.coordinates)
										})
									]
								})
							});
						}

						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }


					    $scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#terrenoCartorialOriginalOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});



						if(terreno != undefined){
									map.addLayer(terrenoLayer);
									map.addLayer(benfeitoriasLayer);
									map.addLayer(unidadeAutonomaLayer);
									map.addLayer(pontoLayer);


								var terrenoExtent = terrenoLayer.getSource().getExtent();
				            		map.getView().fit(terrenoExtent,map.getSize());

				            		view.setCenter(map.getView().getCenter());
							}
	               }else{

	               		$(".mapaTerrenoImovel").css('display','none');
	            	    $(".mapaPontoImovel").css('display','none');
	               }


				});
			}
		});

	}



})();

(function(){

	MapaOriginalBenfeitoriaController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaOriginalBenfeitoriaController", MapaOriginalBenfeitoriaController);

	function MapaOriginalBenfeitoriaController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					if(vm.imovel.rip !== null) {
						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						var styleTerreno;

		                var estiloPonto = new ol.style.Style({
		        			image : new ol.style.Icon({
		    					anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer.png',
				                zIndex: 9999
		        			})
		        		});

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});

						if(terreno != undefined){
									var terrenoLayer = new ol.layer.Vector({
										source : new ol.source.Vector({
											features : [
												new ol.Feature({
													geometry : new ol.geom.Polygon(terreno.coordinates)
												})
											]
										})
									});
							}

							if(terrenoCartorial != undefined){
										var terrenoCartorialLayer = new ol.layer.Vector({
											source : new ol.source.Vector({
												features : [
													new ol.Feature({
														geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
													})
												]
											})
										});
								}

						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }


						$scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#benfeitoriaOriginalOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});


						if(terreno != undefined){
							map.addLayer(terrenoLayer);
						}
						if(terrenoCartorial){
							map.addLayer(terrenoCartorialLayer);
						}
						map.addLayer(benfeitoriasLayer);
						map.addLayer(unidadeAutonomaLayer);
						map.addLayer(pontoLayer);

									if(terreno != undefined){
										var terrenoExtent = terrenoLayer.getSource().getExtent();
													map.getView().fit(terrenoExtent,map.getSize());

													view.setCenter(map.getView().getCenter());
										}else{
											var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
														map.getView().fit(terrenoExtent,map.getSize());

														view.setCenter(map.getView().getCenter());
										}
	               }else{

	               		 $(".mapaBenfeitoriaImovel").css('display','none');
	               }


				});
			}
		});

	}



})();

(function(){

	MapaAlteradoController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaAlteradoController", MapaAlteradoController);

	function MapaAlteradoController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovelOriginal = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;


				if(vm.imovelOriginal === vm.imovelAlterado){
					vm.imovel = vm.imovelOriginal;
				}else{
					vm.imovel = vm.imovelAlterado;
				}

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					var stylePonto = new ol.style.Style({
						image : new ol.style.Icon({
							anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer_off.png',
			                zIndex: 9999
						})
					});
					var styleTerreno;

	                var estiloPonto = new ol.style.Style({
	        			image : new ol.style.Icon({
	    					anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer.png',
			                zIndex: 9999
	        			})
	        		});

	                var pontoLayer = new ol.layer.Vector({
						source : new ol.source.Vector({
							features : [
								new ol.Feature({
									geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
								}),

							]
						}),
						style : stylePonto

					});

					if(terreno != undefined){
							var terrenoLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terreno.coordinates)
										})
									]
								})
							});
					}

					if(terrenoCartorial != undefined){
							var terrenoCartorialLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
										})
									]
								})
							});
						}

					var unidadeAutonomaLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					var benfeitoriasLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					// se houver UA

					if(unidadeAutonoma !== null){
						var feature = new ol.Feature({
							geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
						});
						unidadeAutonomaLayer.getSource().addFeature(feature);
					}

					// se houver benfeitorias

					if(benfeitorias.length > 0){
					      for(var i = 0; i < benfeitorias.length; i++){
					       if(benfeitorias[i].codigo.indexOf("C") != -1){
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }else{
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }
					                  }
					     }



					if(JSON.stringify($scope.imovelOriginal.pontoImovel.coordinates) !== JSON.stringify($scope.imovelAlterado.pontoImovel.coordinates)){
						$(".mapaHomologarAlterado").css('border-style','solid');
						$(".mapaHomologarAlterado").css('border-color','red');
						$scope.$emit('emitSecaoAlterada', 'dadosDoImovel');
						pontoLayer.setStyle(estiloPonto);
					}else{
						$scope.$emit('emitDesabilitarCheckboxMapaImovel');
					}

					$scope.imovel = vm.imovel;

					var elementos = angular.element( document.querySelector( '#terrenoOverlay' ) );

	    			var elemento = elementos[0];

					var overlay = new ol.Overlay({
	            		element: elemento,
	            		stopEvent: false
	            	});

	    			map.addOverlay(overlay);

					map.on("click", function(evt){
	            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
							return feature;
						});
						if(feature){
							var coordenadas = feature.getGeometry().getCoordinates();
							overlay.setPosition(evt.coordinate);
							elemento.setAttribute("style", "display:inline; height:90px;");
						}else{
							overlay.setPosition(undefined);
						}

						feature = "";

	            	});

					if(terreno != undefined){
							map.addLayer(terrenoLayer);
					}
					if(terrenoCartorial != undefined){
							map.addLayer(terrenoCartorialLayer);
						}
					map.addLayer(benfeitoriasLayer);
					map.addLayer(unidadeAutonomaLayer);
					map.addLayer(pontoLayer);

					if(terreno != undefined){
						var terrenoExtent = terrenoLayer.getSource().getExtent();
									map.getView().fit(terrenoExtent,map.getSize());

									view.setCenter(map.getView().getCenter());
						}else{
							var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
										map.getView().fit(terrenoExtent,map.getSize());

										view.setCenter(map.getView().getCenter());
						}

				});
			}
		});

	}



})();

(function(){

	MapaAlteradoUnidadeAutonomaController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaAlteradoUnidadeAutonomaController", MapaAlteradoUnidadeAutonomaController);

	function MapaAlteradoUnidadeAutonomaController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovelOriginal = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;


				if(vm.imovelOriginal === vm.imovelAlterado){
					vm.imovel = vm.imovelOriginal;
				}else{
					vm.imovel = vm.imovelAlterado;
				}

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;

				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					var stylePonto = new ol.style.Style({
						image : new ol.style.Icon({
							anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer_off.png',
			                zIndex: 9999
						})
					});
					var styleTerreno;

	                var estiloPonto = new ol.style.Style({
	        			image : new ol.style.Icon({
	    					anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer.png',
			                zIndex: 9999
	        			})
	        		});

	                var pontoLayer = new ol.layer.Vector({
						source : new ol.source.Vector({
							features : [
								new ol.Feature({
									geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
								}),

							]
						}),
						style : stylePonto

					});


					if(terreno != undefined){
							var terrenoLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terreno.coordinates)
										})
									]
								})
							});
					}

					if(terrenoCartorial != undefined){
							var terrenoCartorialLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
										})
									]
								})
							});
						}

					var unidadeAutonomaLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					var benfeitoriasLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					// se houver UA

					if(unidadeAutonoma !== null){
						var feature = new ol.Feature({
							geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
						});
						unidadeAutonomaLayer.getSource().addFeature(feature);
					}

					// se houver benfeitorias

					if(benfeitorias.length > 0){
					      for(var i = 0; i < benfeitorias.length; i++){
					       if(benfeitorias[i].codigo.indexOf("C") != -1){
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }else{
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }
					                  }
					     }

					if($scope.imovelOriginal.unidadeAutonoma != null){
						if(JSON.stringify($scope.imovelOriginal.unidadeAutonoma.geometriaUnidadeAutonoma.coordinates) !== JSON.stringify($scope.imovelAlterado.unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)){
							var styleUnidadeAutonoma = new ol.style.Style({
							        fill: new ol.style.Stroke({
							          color: '#FF6347'
							        })
							    });

							unidadeAutonomaLayer.setStyle(styleUnidadeAutonoma);
							$(".mapaUnidadeAutonoimaAlterada").css('border-style','solid');
							$(".mapaUnidadeAutonoimaAlterada").css('border-color','red');
							$scope.$emit('emitSecaoAlterada', 'dadosDaUnidadeAutonoma');
						}else{
							$scope.$emit('emitDesabilitarCheckboxMapaUnidadeAutonoma');
						}
					}else{
						if($scope.imovelAlterado.unidadeAutonoma != null){

							var styleUnidadeAutonoma = new ol.style.Style({
						        stroke: new ol.style.Stroke({
						          color: '#FF6347'
						        })
						    });

							unidadeAutonomaLayer.setStyle(styleUnidadeAutonoma);
							$(".mapaUnidadeAutonoimaAlterada").css('border-style','solid');
							$(".mapaUnidadeAutonoimaAlterada").css('border-color','red');
							$scope.$emit('emitSecaoAlterada', 'dadosDaUnidadeAutonoma');
						}else{
							$scope.$emit('emitDesabilitarCheckboxMapaUnidadeAutonoma');
						}
					}


					 $scope.imovel = vm.imovel;

					var elementos = angular.element( document.querySelector( '#unidadeAutonomaOverlay' ) );

	    			var elemento = elementos[0];

					var overlay = new ol.Overlay({
	            		element: elemento,
	            		stopEvent: false
	            	});

	    			map.addOverlay(overlay);

					map.on("click", function(evt){
	            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
							return feature;
						});
						if(feature){
							var coordenadas = feature.getGeometry().getCoordinates();
							overlay.setPosition(evt.coordinate);
							elemento.setAttribute("style", "display:inline; height:90px;");
						}else{
							overlay.setPosition(undefined);
						}

						feature = "";

	            	});



					if(terreno != undefined){
						map.addLayer(terrenoLayer);
					}
					if(terrenoCartorial != undefined){
							map.addLayer(terrenoCartorialLayer);
					}
					map.addLayer(benfeitoriasLayer);
					map.addLayer(unidadeAutonomaLayer);
					map.addLayer(pontoLayer);

					if(terreno != undefined){
						var terrenoExtent = terrenoLayer.getSource().getExtent();
									map.getView().fit(terrenoExtent,map.getSize());

									view.setCenter(map.getView().getCenter());
						}else{
							var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
										map.getView().fit(terrenoExtent,map.getSize());

										view.setCenter(map.getView().getCenter());
						}

				});
			}
		});

	}



})();

(function(){

	MapaAlteradoTerrenoController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaAlteradoTerrenoController", MapaAlteradoTerrenoController);

	function MapaAlteradoTerrenoController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovelOriginal = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;


				if(vm.imovelOriginal === vm.imovelAlterado){
					vm.imovel = vm.imovelOriginal;
				}else{
					vm.imovel = vm.imovelAlterado;
				}

				var terreno = vm.imovel.terreno.geometriaTerreno;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					var stylePonto = new ol.style.Style({
						image : new ol.style.Icon({
							anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer_off.png',
			                zIndex: 9999
						})
					});
					var styleTerreno;

	                var estiloPonto = new ol.style.Style({
	        			image : new ol.style.Icon({
	    					anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer.png',
			                zIndex: 9999
	        			})
	        		});

	                var pontoLayer = new ol.layer.Vector({
						source : new ol.source.Vector({
							features : [
								new ol.Feature({
									geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
								}),

							]
						}),
						style : stylePonto

					});

					if(terreno != undefined){
								var terrenoLayer = new ol.layer.Vector({
									source : new ol.source.Vector({
										features : [
											new ol.Feature({
												geometry : new ol.geom.Polygon(terreno.coordinates)
											})
										]
									})
								});
						}

					var unidadeAutonomaLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					var benfeitoriasLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					// se houver UA

					if(unidadeAutonoma !== null){
						var feature = new ol.Feature({
							geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
						});
						unidadeAutonomaLayer.getSource().addFeature(feature);
					}

					// se houver benfeitorias

					if(benfeitorias.length > 0){
					      for(var i = 0; i < benfeitorias.length; i++){
					       if(benfeitorias[i].codigo.indexOf("C") != -1){
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }else{
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }
					                  }
					     }


					 $scope.imovel = vm.imovel;

					var elementos = angular.element( document.querySelector( '#terrenoCadastralOverlay' ) );

	    			var elemento = elementos[0];

					var overlay = new ol.Overlay({
	            		element: elemento,
	            		stopEvent: false
	            	});

	    			map.addOverlay(overlay);

					map.on("click", function(evt){
	            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
							return feature;
						});
						if(feature){
							var coordenadas = feature.getGeometry().getCoordinates();
							overlay.setPosition(evt.coordinate);
							elemento.setAttribute("style", "display:inline; height:90px;");
						}else{
							overlay.setPosition(undefined);
						}

						feature = "";

	            	});



					if(JSON.stringify($scope.imovelOriginal.terreno.geometriaTerreno) !== JSON.stringify($scope.imovelAlterado.terreno.geometriaTerreno)){
						var styleTerreno = new ol.style.Style({
						         stroke: new ol.style.Stroke({
						          color: '#FF6347'
						        })
						    });

						terrenoLayer.setStyle(styleTerreno);
						$(".mapaTerrenoAlterado").css('border-style','solid');
						$(".mapaTerrenoAlterado").css('border-color','red');
						$scope.$emit('emitSecaoAlterada', 'dadosDoTerreno');


					}else{
						$scope.$emit('emitDesabilitarCheckboxTerreno');
					}
					if(terreno != undefined){
						map.addLayer(terrenoLayer);
					}
					map.addLayer(benfeitoriasLayer);
					map.addLayer(unidadeAutonomaLayer);
					map.addLayer(pontoLayer);
						if(terreno != undefined){
							var terrenoExtent = terrenoLayer.getSource().getExtent();
			            		map.getView().fit(terrenoExtent,map.getSize());

			            		view.setCenter(map.getView().getCenter());
						}

				});
			}
		});

	}



})();

(function(){

	MapaAlteradoTerrenoCartorialController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaAlteradoTerrenoCartorialController", MapaAlteradoTerrenoCartorialController);

	function MapaAlteradoTerrenoCartorialController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);

						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						var styleTerreno;

		                var estiloPonto = new ol.style.Style({
		        			image : new ol.style.Icon({
		    					anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer.png',
				                zIndex: 9999
		        			})
		        		});

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});

						if(terreno != undefined){
							var terrenoLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terreno.coordinates)
										})
									]
								})
							});
						}

						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }

					    $scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#terrenoCartorialOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});


						if(terreno != undefined){
							map.addLayer(terrenoLayer);
							map.addLayer(benfeitoriasLayer);
							map.addLayer(unidadeAutonomaLayer);
							map.addLayer(pontoLayer);

							var terrenoExtent = terrenoLayer.getSource().getExtent();
			            		map.getView().fit(terrenoExtent,map.getSize());

			            		view.setCenter(map.getView().getCenter());

		            	}


				});
			}
		});

	}



})();


(function(){

	MapaAlteradoBenfeitoriaController.$inject = ["$scope", "olData", "ContadorService"];
	angular.module("su-cadastro-imovel").controller("MapaAlteradoBenfeitoriaController", MapaAlteradoBenfeitoriaController);

	function MapaAlteradoBenfeitoriaController($scope, olData, ContadorService){

		var vm = this;

		var item = $scope.$parent.item;

		var index = $scope.$parent.$index;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){


				vm.imovelOriginal = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;


				if(vm.imovelOriginal === vm.imovelAlterado){
					vm.imovel = vm.imovelOriginal;
				}else{
					vm.imovel = vm.imovelAlterado;
				}

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;

                $scope.imovel = vm.imovel;


				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					var stylePonto = new ol.style.Style({
						image : new ol.style.Icon({
							anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer_off.png',
			                zIndex: 9999
						})
					});
					var styleTerreno;

	                var estiloPonto = new ol.style.Style({
	        			image : new ol.style.Icon({
	    					anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer.png',
			                zIndex: 9999
	        			})
	        		});

	                var pontoLayer = new ol.layer.Vector({
						source : new ol.source.Vector({
							features : [
								new ol.Feature({
									geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
								}),

							]
						}),
						style : stylePonto

					});

					var benfeitoriaEspecificaLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					var benfeitoriaEspecificaLayerAzul = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					if(terreno != undefined){
							var terrenoLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terreno.coordinates)
										})
									]
								})
							});
					}


					if(terrenoCartorial != undefined){
							var terrenoCartorialLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
										})
									]
								})
							});
						}

					var unidadeAutonomaLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					var benfeitoriasLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					// se houver UA

					if(unidadeAutonoma !== null){
						var feature = new ol.Feature({
							geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
						});
						unidadeAutonomaLayer.getSource().addFeature(feature);
					}

					// se houver benfeitorias

					if(benfeitorias.length > 0){
					      for(var i = 0; i < benfeitorias.length; i++){
					       if(benfeitorias[i].codigo.indexOf("C") != -1){
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);


					                   }else{
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
					                    });
					                    	benfeitoriasLayer.getSource().addFeature(feature);

					                   }

					          }
					     }

					     var styleBenfeitoria = new ol.style.Style({
							   stroke: new ol.style.Stroke({
							    color: '#FF6347',
							    width: 3

							  })
						});

						var styleBenfeitoriaAzul = new ol.style.Style({
							   stroke: new ol.style.Stroke({
							    color: '#00008B',
							    width: 3
							  })
						});




						benfeitorias.sort(function(a, b){
						    if(a.codigo < b.codigo) return -1;
						    if(a.codigo > b.codigo) return 1;
						    return 0;
						});



						var benfeitoriaOriginal =  vm.imovelOriginal.benfeitorias[ContadorService.getContador()];
						var benfeitoriaAlterada = vm.imovelAlterado.benfeitorias[ContadorService.getContador()];

						var geometriaComplementarOriginal;
						var geometriaComplementarAlterada;
						var geometriaEdificacaoOriginal;
						var geometriaEdificacaoAlterada;


						if(benfeitoriaOriginal != undefined){
							geometriaComplementarOriginal =  benfeitoriaOriginal.benfeitoriaComplementar == undefined ? undefined : benfeitoriaOriginal.benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates;
							geometriaEdificacaoOriginal = benfeitoriaOriginal.edificacao == undefined ? undefined : benfeitoriaOriginal.edificacao.geometriaEdificacao.coordinates;
						}

						if(benfeitoriaAlterada != undefined){
							geometriaComplementarAlterada =  benfeitoriaAlterada.benfeitoriaComplementar == undefined ? undefined : benfeitoriaAlterada.benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates;
							geometriaEdificacaoAlterada = benfeitoriaAlterada.edificacao == undefined ? undefined : benfeitoriaAlterada.edificacao.geometriaEdificacao.coordinates;
						}


						if(geometriaComplementarAlterada!= undefined){
							if(JSON.stringify(geometriaComplementarOriginal) !== JSON.stringify(geometriaComplementarAlterada)){
									 var feature = new ol.Feature({
									           geometry : new ol.geom.Polygon(geometriaComplementarAlterada)
									       });
									benfeitoriaEspecificaLayer.getSource().addFeature(feature);
									$scope.$emit('emitBenfeitoriaAlterada', benfeitoriaAlterada);
								}else if(JSON.stringify(geometriaComplementarOriginal) === JSON.stringify(geometriaComplementarAlterada)){
									var feature = new ol.Feature({
								               geometry : new ol.geom.Polygon(geometriaComplementarAlterada)
								           });
								    benfeitoriaEspecificaLayerAzul.getSource().addFeature(feature);
									$scope.$emit('emitDesabilitarCheckboxMapaBenfeitoria', benfeitoriaAlterada);
								}

						}else if(geometriaEdificacaoAlterada != undefined){
								if(JSON.stringify(geometriaEdificacaoOriginal) === JSON.stringify(geometriaEdificacaoAlterada)){
									var feature = new ol.Feature({
								               geometry : new ol.geom.Polygon(geometriaEdificacaoAlterada)
								           });
								    benfeitoriaEspecificaLayerAzul.getSource().addFeature(feature);
								    $scope.$emit('emitDesabilitarCheckboxMapaBenfeitoria', benfeitoriaAlterada);
								}

								else if(JSON.stringify(geometriaEdificacaoOriginal) !== JSON.stringify(geometriaEdificacaoAlterada)){
									var feature = new ol.Feature({
								               geometry : new ol.geom.Polygon(geometriaEdificacaoAlterada)
								           });
								    benfeitoriaEspecificaLayer.getSource().addFeature(feature);
								    $scope.$emit('emitBenfeitoriaAlterada', benfeitoriaAlterada);
								}
						}



						benfeitoriaEspecificaLayer.setStyle(styleBenfeitoria);
						benfeitoriaEspecificaLayerAzul.setStyle(styleBenfeitoriaAzul);

						$(".mapaBenfeitoriaAlterada"+ContadorService.getContador()).css('border-style','solid');
						$(".mapaBenfeitoriaAlterada"+ContadorService.getContador()).css('border-color','red');





					if(ContadorService.getContador() > vm.imovelAlterado.benfeitorias.length-1){
						ContadorService.setContador(0);
					}else{
						ContadorService.setContador(ContadorService.getContador()+1);
					}

					if(terreno != undefined){
						map.addLayer(terrenoLayer);
					}
					if(terrenoCartorial != undefined){
							map.addLayer(terrenoCartorialLayer);
					}


					var elementos = angular.element( document.querySelector( '#overlay' ) );

	    			var elemento = elementos[0];

					var overlay = new ol.Overlay({
	            		element: elemento,
	            		stopEvent: false
	            	});

	    			map.addOverlay(overlay);

					map.on("click", function(evt){
	            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
							return feature;
						});
						if(feature){
							var coordenadas = feature.getGeometry().getCoordinates();
							overlay.setPosition(evt.coordinate);
							elemento.setAttribute("style", "display:inline; height:90px;");
						}else{
							overlay.setPosition(undefined);
						}

						feature = "";

	            	});

					map.addLayer(benfeitoriasLayer);
					map.addLayer(benfeitoriaEspecificaLayer);
					map.addLayer(benfeitoriaEspecificaLayerAzul);
					map.addLayer(unidadeAutonomaLayer);
					map.addLayer(pontoLayer);

					if(terreno != undefined){
						var terrenoExtent = terrenoLayer.getSource().getExtent();
									map.getView().fit(terrenoExtent,map.getSize());

									view.setCenter(map.getView().getCenter());
						}else{
							var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
										map.getView().fit(terrenoExtent,map.getSize());

										view.setCenter(map.getView().getCenter());
						}

				});
			}
		});


	}



})();

(function(){

	MapaOriginalController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaOriginalController", MapaOriginalController);

	function MapaOriginalController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					if(vm.imovel.rip !== null) {
						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						// var styleTerreno;
                        //
		                // var estiloPonto = new ol.style.Style({
		        		// 	image : new ol.style.Icon({
		    			// 		anchor: [0.5, 32],
				        //         anchorXUnits: 'fraction',
				        //         anchorYUnits: 'pixels',
				        //         src: 'assets/img/icons/pointer.png',
				        //         zIndex: 9999
		        		// 	})
		        		// });

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});

						if(terreno != undefined){
								var terrenoLayer = new ol.layer.Vector({
									source : new ol.source.Vector({
										features : [
											new ol.Feature({
												geometry : new ol.geom.Polygon(terreno.coordinates)
											})
										]
									})
								});
						}

						if(terrenoCartorial != undefined){
							var terrenoCartorialLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
										})
									]
								})
							});
						}

						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }



						 $scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#mapaOriginalOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});

						if(terreno != undefined){
								 map.addLayer(terrenoLayer);
						}
						if(terrenoCartorial != undefined){
							map.addLayer(terrenoCartorialLayer);
						}
						map.addLayer(benfeitoriasLayer);
						map.addLayer(unidadeAutonomaLayer);
						map.addLayer(pontoLayer);

									if(terreno != undefined){
										var terrenoExtent = terrenoLayer.getSource().getExtent();
													map.getView().fit(terrenoExtent,map.getSize());

													view.setCenter(map.getView().getCenter());
										}else{
											var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
														map.getView().fit(terrenoExtent,map.getSize());

														view.setCenter(map.getView().getCenter());
										}
	               }else{

	               		$("#mapaPontoImovel").css('display','none');

	               }


				});
			}
		});

	}



})();

(function(){

	MapaOriginalUnidadeAutonomaController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaOriginalUnidadeAutonomaController", MapaOriginalUnidadeAutonomaController);

	function MapaOriginalUnidadeAutonomaController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					if(vm.imovel.rip !== null) {
						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						var styleTerreno;

		                var estiloPonto = new ol.style.Style({
		        			image : new ol.style.Icon({
		    					anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer.png',
				                zIndex: 9999
		        			})
		        		});

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});


						if(terreno != undefined){
								var terrenoLayer = new ol.layer.Vector({
									source : new ol.source.Vector({
										features : [
											new ol.Feature({
												geometry : new ol.geom.Polygon(terreno.coordinates)
											})
										]
									})
								});
						}


						if(terrenoCartorial != undefined){
								var terrenoCartorialLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
										features : [
											new ol.Feature({
												geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
											})
										]
									})
								});
							}


						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }



						$scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#unidadeAutonomaOriginalOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});

						if(terreno != undefined){
	 						map.addLayer(terrenoLayer);
	 					}
	 					if(terrenoCartorial != undefined){
	 							map.addLayer(terrenoCartorialLayer);
	 					}
						map.addLayer(benfeitoriasLayer);
						map.addLayer(unidadeAutonomaLayer);
						map.addLayer(pontoLayer);
									if(terreno != undefined){
										var terrenoExtent = terrenoLayer.getSource().getExtent();
					            		map.getView().fit(terrenoExtent,map.getSize());

					            		view.setCenter(map.getView().getCenter());
										}else{
											var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
														map.getView().fit(terrenoExtent,map.getSize());

														view.setCenter(map.getView().getCenter());
										}
	               }else{

	               	    $(".mapaUnidadeAutonoma").css('display','none');

				   }


				});
			}
		});

	}



})();

(function(){

	MapaOriginalTerrenoController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaOriginalTerrenoController", MapaOriginalTerrenoController);

	function MapaOriginalTerrenoController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerreno;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					if(vm.imovel.rip !== null) {
						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						var styleTerreno;

		                var estiloPonto = new ol.style.Style({
		        			image : new ol.style.Icon({
		    					anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer.png',
				                zIndex: 9999
		        			})
		        		});

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});

							if(terreno != undefined){
									var terrenoLayer = new ol.layer.Vector({
										source : new ol.source.Vector({
											features : [
												new ol.Feature({
													geometry : new ol.geom.Polygon(terreno.coordinates)
												})
											]
										})
									});
							}

						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }

						$scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#terrenoOriginalOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});


						if(terreno != undefined){
							map.addLayer(terrenoLayer);
						}
						map.addLayer(benfeitoriasLayer);
						map.addLayer(unidadeAutonomaLayer);
						map.addLayer(pontoLayer);

									if(terreno != undefined){
										var terrenoExtent = terrenoLayer.getSource().getExtent();
													map.getView().fit(terrenoExtent,map.getSize());

													view.setCenter(map.getView().getCenter());
										}
	               }else{

	               		$(".mapaTerrenoImovel").css('display','none');
	            	    $(".mapaPontoImovel").css('display','none');
	               }


				});
			}
		});

	}



})();

(function(){

	MapaOriginalTerrenoCartorialController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaOriginalTerrenoCartorialController", MapaOriginalTerrenoCartorialController);

	function MapaOriginalTerrenoCartorialController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					if(vm.imovel.rip !== null) {
						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						var styleTerreno;

		                var estiloPonto = new ol.style.Style({
		        			image : new ol.style.Icon({
		    					anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer.png',
				                zIndex: 9999
		        			})
		        		});

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});

						if(terreno != undefined){
							var terrenoLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terreno.coordinates)
										})
									]
								})
							});
						}

						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }


					    $scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#terrenoCartorialOriginalOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});



						if(terreno != undefined){
									map.addLayer(terrenoLayer);
									map.addLayer(benfeitoriasLayer);
									map.addLayer(unidadeAutonomaLayer);
									map.addLayer(pontoLayer);


								var terrenoExtent = terrenoLayer.getSource().getExtent();
				            		map.getView().fit(terrenoExtent,map.getSize());

				            		view.setCenter(map.getView().getCenter());
							}
	               }else{

	               		$(".mapaTerrenoImovel").css('display','none');
	            	    $(".mapaPontoImovel").css('display','none');
	               }


				});
			}
		});

	}



})();

(function(){

	MapaOriginalBenfeitoriaController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaOriginalBenfeitoriaController", MapaOriginalBenfeitoriaController);

	function MapaOriginalBenfeitoriaController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					if(vm.imovel.rip !== null) {
						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						var styleTerreno;

		                var estiloPonto = new ol.style.Style({
		        			image : new ol.style.Icon({
		    					anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer.png',
				                zIndex: 9999
		        			})
		        		});

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});

						if(terreno != undefined){
									var terrenoLayer = new ol.layer.Vector({
										source : new ol.source.Vector({
											features : [
												new ol.Feature({
													geometry : new ol.geom.Polygon(terreno.coordinates)
												})
											]
										})
									});
							}

							if(terrenoCartorial != undefined){
										var terrenoCartorialLayer = new ol.layer.Vector({
											source : new ol.source.Vector({
												features : [
													new ol.Feature({
														geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
													})
												]
											})
										});
								}

						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }


						$scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#benfeitoriaOriginalOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});


						if(terreno != undefined){
							map.addLayer(terrenoLayer);
						}
						if(terrenoCartorial){
							map.addLayer(terrenoCartorialLayer);
						}
						map.addLayer(benfeitoriasLayer);
						map.addLayer(unidadeAutonomaLayer);
						map.addLayer(pontoLayer);

									if(terreno != undefined){
										var terrenoExtent = terrenoLayer.getSource().getExtent();
													map.getView().fit(terrenoExtent,map.getSize());

													view.setCenter(map.getView().getCenter());
										}else{
											var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
														map.getView().fit(terrenoExtent,map.getSize());

														view.setCenter(map.getView().getCenter());
										}
	               }else{

	               		 $(".mapaBenfeitoriaImovel").css('display','none');
	               }


				});
			}
		});

	}



})();

(function(){

	MapaAlteradoController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaAlteradoController", MapaAlteradoController);

	function MapaAlteradoController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovelOriginal = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;


				if(vm.imovelOriginal === vm.imovelAlterado){
					vm.imovel = vm.imovelOriginal;
				}else{
					vm.imovel = vm.imovelAlterado;
				}

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					var stylePonto = new ol.style.Style({
						image : new ol.style.Icon({
							anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer_off.png',
			                zIndex: 9999
						})
					});
					var styleTerreno;

	                var estiloPonto = new ol.style.Style({
	        			image : new ol.style.Icon({
	    					anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer.png',
			                zIndex: 9999
	        			})
	        		});

	                var pontoLayer = new ol.layer.Vector({
						source : new ol.source.Vector({
							features : [
								new ol.Feature({
									geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
								}),

							]
						}),
						style : stylePonto

					});

					if(terreno != undefined){
							var terrenoLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terreno.coordinates)
										})
									]
								})
							});
					}

					if(terrenoCartorial != undefined){
							var terrenoCartorialLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
										})
									]
								})
							});
						}

					var unidadeAutonomaLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					var benfeitoriasLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					// se houver UA

					if(unidadeAutonoma !== null){
						var feature = new ol.Feature({
							geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
						});
						unidadeAutonomaLayer.getSource().addFeature(feature);
					}

					// se houver benfeitorias

					if(benfeitorias.length > 0){
					      for(var i = 0; i < benfeitorias.length; i++){
					       if(benfeitorias[i].codigo.indexOf("C") != -1){
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }else{
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }
					                  }
					     }



					if(JSON.stringify($scope.imovelOriginal.pontoImovel.coordinates) !== JSON.stringify($scope.imovelAlterado.pontoImovel.coordinates)){
						$(".mapaHomologarAlterado").css('border-style','solid');
						$(".mapaHomologarAlterado").css('border-color','red');
						$scope.$emit('emitSecaoAlterada', 'dadosDoImovel');
						pontoLayer.setStyle(estiloPonto);
					}else{
						$scope.$emit('emitDesabilitarCheckboxMapaImovel');
					}

					$scope.imovel = vm.imovel;

					var elementos = angular.element( document.querySelector( '#terrenoOverlay' ) );

	    			var elemento = elementos[0];

					var overlay = new ol.Overlay({
	            		element: elemento,
	            		stopEvent: false
	            	});

	    			map.addOverlay(overlay);

					map.on("click", function(evt){
	            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
							return feature;
						});
						if(feature){
							var coordenadas = feature.getGeometry().getCoordinates();
							overlay.setPosition(evt.coordinate);
							elemento.setAttribute("style", "display:inline; height:90px;");
						}else{
							overlay.setPosition(undefined);
						}

						feature = "";

	            	});

					if(terreno != undefined){
							map.addLayer(terrenoLayer);
					}
					if(terrenoCartorial != undefined){
							map.addLayer(terrenoCartorialLayer);
						}
					map.addLayer(benfeitoriasLayer);
					map.addLayer(unidadeAutonomaLayer);
					map.addLayer(pontoLayer);

					if(terreno != undefined){
						var terrenoExtent = terrenoLayer.getSource().getExtent();
									map.getView().fit(terrenoExtent,map.getSize());

									view.setCenter(map.getView().getCenter());
						}else{
							var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
										map.getView().fit(terrenoExtent,map.getSize());

										view.setCenter(map.getView().getCenter());
						}

				});
			}
		});

	}



})();

(function(){

	MapaAlteradoUnidadeAutonomaController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaAlteradoUnidadeAutonomaController", MapaAlteradoUnidadeAutonomaController);

	function MapaAlteradoUnidadeAutonomaController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovelOriginal = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;


				if(vm.imovelOriginal === vm.imovelAlterado){
					vm.imovel = vm.imovelOriginal;
				}else{
					vm.imovel = vm.imovelAlterado;
				}

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;

				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					var stylePonto = new ol.style.Style({
						image : new ol.style.Icon({
							anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer_off.png',
			                zIndex: 9999
						})
					});
					var styleTerreno;

	                var estiloPonto = new ol.style.Style({
	        			image : new ol.style.Icon({
	    					anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer.png',
			                zIndex: 9999
	        			})
	        		});

	                var pontoLayer = new ol.layer.Vector({
						source : new ol.source.Vector({
							features : [
								new ol.Feature({
									geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
								}),

							]
						}),
						style : stylePonto

					});


					if(terreno != undefined){
							var terrenoLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terreno.coordinates)
										})
									]
								})
							});
					}

					if(terrenoCartorial != undefined){
							var terrenoCartorialLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
										})
									]
								})
							});
						}

					var unidadeAutonomaLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					var benfeitoriasLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					// se houver UA

					if(unidadeAutonoma !== null){
						var feature = new ol.Feature({
							geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
						});
						unidadeAutonomaLayer.getSource().addFeature(feature);
					}

					// se houver benfeitorias

					if(benfeitorias.length > 0){
					      for(var i = 0; i < benfeitorias.length; i++){
					       if(benfeitorias[i].codigo.indexOf("C") != -1){
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }else{
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }
					                  }
					     }

					if($scope.imovelOriginal.unidadeAutonoma != null){
						if(JSON.stringify($scope.imovelOriginal.unidadeAutonoma.geometriaUnidadeAutonoma.coordinates) !== JSON.stringify($scope.imovelAlterado.unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)){
							var styleUnidadeAutonoma = new ol.style.Style({
							        fill: new ol.style.Stroke({
							          color: '#FF6347'
							        })
							    });

							unidadeAutonomaLayer.setStyle(styleUnidadeAutonoma);
							$(".mapaUnidadeAutonoimaAlterada").css('border-style','solid');
							$(".mapaUnidadeAutonoimaAlterada").css('border-color','red');
							$scope.$emit('emitSecaoAlterada', 'dadosDaUnidadeAutonoma');
						}else{
							$scope.$emit('emitDesabilitarCheckboxMapaUnidadeAutonoma');
						}
					}else{
						if($scope.imovelAlterado.unidadeAutonoma != null){

							var styleUnidadeAutonoma = new ol.style.Style({
						        stroke: new ol.style.Stroke({
						          color: '#FF6347'
						        })
						    });

							unidadeAutonomaLayer.setStyle(styleUnidadeAutonoma);
							$(".mapaUnidadeAutonoimaAlterada").css('border-style','solid');
							$(".mapaUnidadeAutonoimaAlterada").css('border-color','red');
							$scope.$emit('emitSecaoAlterada', 'dadosDaUnidadeAutonoma');
						}else{
							$scope.$emit('emitDesabilitarCheckboxMapaUnidadeAutonoma');
						}
					}


					 $scope.imovel = vm.imovel;

					var elementos = angular.element( document.querySelector( '#unidadeAutonomaOverlay' ) );

	    			var elemento = elementos[0];

					var overlay = new ol.Overlay({
	            		element: elemento,
	            		stopEvent: false
	            	});

	    			map.addOverlay(overlay);

					map.on("click", function(evt){
	            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
							return feature;
						});
						if(feature){
							var coordenadas = feature.getGeometry().getCoordinates();
							overlay.setPosition(evt.coordinate);
							elemento.setAttribute("style", "display:inline; height:90px;");
						}else{
							overlay.setPosition(undefined);
						}

						feature = "";

	            	});



					if(terreno != undefined){
						map.addLayer(terrenoLayer);
					}
					if(terrenoCartorial != undefined){
							map.addLayer(terrenoCartorialLayer);
					}
					map.addLayer(benfeitoriasLayer);
					map.addLayer(unidadeAutonomaLayer);
					map.addLayer(pontoLayer);

					if(terreno != undefined){
						var terrenoExtent = terrenoLayer.getSource().getExtent();
									map.getView().fit(terrenoExtent,map.getSize());

									view.setCenter(map.getView().getCenter());
						}else{
							var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
										map.getView().fit(terrenoExtent,map.getSize());

										view.setCenter(map.getView().getCenter());
						}

				});
			}
		});

	}



})();

(function(){

	MapaAlteradoTerrenoController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaAlteradoTerrenoController", MapaAlteradoTerrenoController);

	function MapaAlteradoTerrenoController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovelOriginal = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;


				if(vm.imovelOriginal === vm.imovelAlterado){
					vm.imovel = vm.imovelOriginal;
				}else{
					vm.imovel = vm.imovelAlterado;
				}

				var terreno = vm.imovel.terreno.geometriaTerreno;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);
					var stylePonto = new ol.style.Style({
						image : new ol.style.Icon({
							anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer_off.png',
			                zIndex: 9999
						})
					});
					var styleTerreno;

	                var estiloPonto = new ol.style.Style({
	        			image : new ol.style.Icon({
	    					anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer.png',
			                zIndex: 9999
	        			})
	        		});

	                var pontoLayer = new ol.layer.Vector({
						source : new ol.source.Vector({
							features : [
								new ol.Feature({
									geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
								}),

							]
						}),
						style : stylePonto

					});

					if(terreno != undefined){
								var terrenoLayer = new ol.layer.Vector({
									source : new ol.source.Vector({
										features : [
											new ol.Feature({
												geometry : new ol.geom.Polygon(terreno.coordinates)
											})
										]
									})
								});
						}

					var unidadeAutonomaLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					var benfeitoriasLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					// se houver UA

					if(unidadeAutonoma !== null){
						var feature = new ol.Feature({
							geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
						});
						unidadeAutonomaLayer.getSource().addFeature(feature);
					}

					// se houver benfeitorias

					if(benfeitorias.length > 0){
					      for(var i = 0; i < benfeitorias.length; i++){
					       if(benfeitorias[i].codigo.indexOf("C") != -1){
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }else{
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);
					                   }
					                  }
					     }


					 $scope.imovel = vm.imovel;

					var elementos = angular.element( document.querySelector( '#terrenoCadastralOverlay' ) );

	    			var elemento = elementos[0];

					var overlay = new ol.Overlay({
	            		element: elemento,
	            		stopEvent: false
	            	});

	    			map.addOverlay(overlay);

					map.on("click", function(evt){
	            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
							return feature;
						});
						if(feature){
							var coordenadas = feature.getGeometry().getCoordinates();
							overlay.setPosition(evt.coordinate);
							elemento.setAttribute("style", "display:inline; height:90px;");
						}else{
							overlay.setPosition(undefined);
						}

						feature = "";

	            	});



					if(JSON.stringify($scope.imovelOriginal.terreno.geometriaTerreno) !== JSON.stringify($scope.imovelAlterado.terreno.geometriaTerreno)){
						var styleTerreno = new ol.style.Style({
						         stroke: new ol.style.Stroke({
						          color: '#FF6347'
						        })
						    });

						terrenoLayer.setStyle(styleTerreno);
						$(".mapaTerrenoAlterado").css('border-style','solid');
						$(".mapaTerrenoAlterado").css('border-color','red');
						$scope.$emit('emitSecaoAlterada', 'dadosDoTerreno');


					}else{
						$scope.$emit('emitDesabilitarCheckboxTerreno');
					}
					if(terreno != undefined){
						map.addLayer(terrenoLayer);
					}
					map.addLayer(benfeitoriasLayer);
					map.addLayer(unidadeAutonomaLayer);
					map.addLayer(pontoLayer);
						if(terreno != undefined){
							var terrenoExtent = terrenoLayer.getSource().getExtent();
			            		map.getView().fit(terrenoExtent,map.getSize());

			            		view.setCenter(map.getView().getCenter());
						}

				});
			}
		});

	}



})();

(function(){

	MapaAlteradoTerrenoCartorialController.$inject = ["$scope", "olData"];
	angular.module("su-cadastro-imovel").controller("MapaAlteradoTerrenoCartorialController", MapaAlteradoTerrenoCartorialController);

	function MapaAlteradoTerrenoCartorialController($scope, olData){

		var vm = this;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){

				vm.imovel = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;

				var terreno = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;



				olData.getMap().then(function(map){

					var view = map.getView();
					view.setZoom(19);

						var stylePonto = new ol.style.Style({
							image : new ol.style.Icon({
								anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer_off.png',
				                zIndex: 9999
							})
						});
						var styleTerreno;

		                var estiloPonto = new ol.style.Style({
		        			image : new ol.style.Icon({
		    					anchor: [0.5, 32],
				                anchorXUnits: 'fraction',
				                anchorYUnits: 'pixels',
				                src: 'assets/img/icons/pointer.png',
				                zIndex: 9999
		        			})
		        		});

		                var pontoLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
								features : [
									new ol.Feature({
										geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
									}),

								]
							}),
							style : stylePonto

						});

						if(terreno != undefined){
							var terrenoLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terreno.coordinates)
										})
									]
								})
							});
						}

						var unidadeAutonomaLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						var benfeitoriasLayer = new ol.layer.Vector({
							source : new ol.source.Vector()
						});

						// se houver UA

						if(unidadeAutonoma !== null){
							var feature = new ol.Feature({
								geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
							});
							unidadeAutonomaLayer.getSource().addFeature(feature);
						}

						// se houver benfeitorias

						if(benfeitorias.length > 0){
						      for(var i = 0; i < benfeitorias.length; i++){
						       if(benfeitorias[i].codigo.indexOf("C") != -1){
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }else{
						                    var feature = new ol.Feature({
						                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
						                    });
						                    benfeitoriasLayer.getSource().addFeature(feature);
						                   }
						                  }
						     }

					    $scope.imovel = vm.imovel;

						var elementos = angular.element( document.querySelector( '#terrenoCartorialOverlay' ) );

		    			var elemento = elementos[0];

						var overlay = new ol.Overlay({
		            		element: elemento,
		            		stopEvent: false
		            	});

		    			map.addOverlay(overlay);

						map.on("click", function(evt){
		            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
								return feature;
							});
							if(feature){
								var coordenadas = feature.getGeometry().getCoordinates();
								overlay.setPosition(evt.coordinate);
								elemento.setAttribute("style", "display:inline; height:90px;");
							}else{
								overlay.setPosition(undefined);
							}

							feature = "";

		            	});


						if(terreno != undefined){
							map.addLayer(terrenoLayer);
							map.addLayer(benfeitoriasLayer);
							map.addLayer(unidadeAutonomaLayer);
							map.addLayer(pontoLayer);

							var terrenoExtent = terrenoLayer.getSource().getExtent();
			            		map.getView().fit(terrenoExtent,map.getSize());

			            		view.setCenter(map.getView().getCenter());

		            	}


				});
			}
		});

	}



})();


(function(){

	MapaAlteradoBenfeitoriaController.$inject = ["$scope", "olData", "ContadorService"];
	angular.module("su-cadastro-imovel").controller("MapaAlteradoBenfeitoriaController", MapaAlteradoBenfeitoriaController);

	function MapaAlteradoBenfeitoriaController($scope, olData, ContadorService){

		var vm = this;

		var item = $scope.$parent.item;

		var index = $scope.$parent.$index;

		angular.extend($scope, {
			defaults : {
				controls: [
	                {name: 'zoom'},
	                {name: 'fullscreen'},
	                {name: 'scaleline'}
            	],
            	interactions: {
                    mouseWheelZoom: true,
                    doubleClickZoom: false
                }
			},
			defaultProjection: 'EPSG:4674',
			init : function(){


				vm.imovelOriginal = $scope.$parent.imovelOriginal;
				vm.imovelAlterado = $scope.$parent.imovelAlterado;


				if(vm.imovelOriginal === vm.imovelAlterado){
					vm.imovel = vm.imovelOriginal;
				}else{
					vm.imovel = vm.imovelAlterado;
				}

				var terreno = vm.imovel.terreno.geometriaTerreno;
				var terrenoCartorial = vm.imovel.terreno.geometriaTerrenoCartorial;
                var benfeitorias = vm.imovel.benfeitorias;
                var unidadeAutonoma = vm.imovel.unidadeAutonoma;

                $scope.imovel = vm.imovel;


				olData.getMap().then(function(map){

					var view = map.getView();
          view.setZoom(19);
					var stylePonto = new ol.style.Style({
						image : new ol.style.Icon({
							anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer_off.png',
			                zIndex: 9999
						})
					});
					var styleTerreno;

	                var estiloPonto = new ol.style.Style({
	        			image : new ol.style.Icon({
	    					anchor: [0.5, 32],
			                anchorXUnits: 'fraction',
			                anchorYUnits: 'pixels',
			                src: 'assets/img/icons/pointer.png',
			                zIndex: 9999
	        			})
	        		});

	                var pontoLayer = new ol.layer.Vector({
						source : new ol.source.Vector({
							features : [
								new ol.Feature({
									geometry : new ol.geom.Point(vm.imovel.pontoImovel.coordinates),
								}),

							]
						}),
						style : stylePonto

					});

					var benfeitoriaEspecificaLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					var benfeitoriaEspecificaLayerAzul = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					if(terreno != undefined){
							var terrenoLayer = new ol.layer.Vector({
								source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terreno.coordinates)
										})
									]
								})
							});
					}


					if(terrenoCartorial != undefined){
							var terrenoCartorialLayer = new ol.layer.Vector({
							source : new ol.source.Vector({
									features : [
										new ol.Feature({
											geometry : new ol.geom.Polygon(terrenoCartorial.coordinates)
										})
									]
								})
							});
						}

					var unidadeAutonomaLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					var benfeitoriasLayer = new ol.layer.Vector({
						source : new ol.source.Vector()
					});

					// se houver UA

					if(unidadeAutonoma !== null){
						var feature = new ol.Feature({
							geometry : new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates)
						});
						unidadeAutonomaLayer.getSource().addFeature(feature);
					}

					// se houver benfeitorias

					if(benfeitorias.length > 0){
					      for(var i = 0; i < benfeitorias.length; i++){
					       if(benfeitorias[i].codigo.indexOf("C") != -1){
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates)
					                    });
					                    benfeitoriasLayer.getSource().addFeature(feature);


					                   }else{
					                    var feature = new ol.Feature({
					                     geometry : new ol.geom.Polygon(benfeitorias[i].edificacao.geometriaEdificacao.coordinates)
					                    });
					                    	benfeitoriasLayer.getSource().addFeature(feature);

					                   }

					          }
					     }

					     var styleBenfeitoria = new ol.style.Style({
							   stroke: new ol.style.Stroke({
							    color: '#FF6347',
							    width: 3

							  })
						});

						var styleBenfeitoriaAzul = new ol.style.Style({
							   stroke: new ol.style.Stroke({
							    color: '#00008B',
							    width: 3
							  })
						});




						benfeitorias.sort(function(a, b){
						    if(a.codigo < b.codigo) return -1;
						    if(a.codigo > b.codigo) return 1;
						    return 0;
						});



						var benfeitoriaOriginal =  vm.imovelOriginal.benfeitorias[ContadorService.getContador()];
						var benfeitoriaAlterada = vm.imovelAlterado.benfeitorias[ContadorService.getContador()];

						var geometriaComplementarOriginal;
						var geometriaComplementarAlterada;
						var geometriaEdificacaoOriginal;
						var geometriaEdificacaoAlterada;


						if(benfeitoriaOriginal != undefined){
							geometriaComplementarOriginal =  benfeitoriaOriginal.benfeitoriaComplementar == undefined ? undefined : benfeitoriaOriginal.benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates;
							geometriaEdificacaoOriginal = benfeitoriaOriginal.edificacao == undefined ? undefined : benfeitoriaOriginal.edificacao.geometriaEdificacao.coordinates;
						}

						if(benfeitoriaAlterada != undefined){
							geometriaComplementarAlterada =  benfeitoriaAlterada.benfeitoriaComplementar == undefined ? undefined : benfeitoriaAlterada.benfeitoriaComplementar.geometriaBenfeitoriaComplementar.coordinates;
							geometriaEdificacaoAlterada = benfeitoriaAlterada.edificacao == undefined ? undefined : benfeitoriaAlterada.edificacao.geometriaEdificacao.coordinates;
						}


						if(geometriaComplementarAlterada!= undefined){
							if(JSON.stringify(geometriaComplementarOriginal) !== JSON.stringify(geometriaComplementarAlterada)){
									 var feature = new ol.Feature({
									           geometry : new ol.geom.Polygon(geometriaComplementarAlterada)
									       });
									benfeitoriaEspecificaLayer.getSource().addFeature(feature);
									$scope.$emit('emitBenfeitoriaAlterada', benfeitoriaAlterada);
								}else if(JSON.stringify(geometriaComplementarOriginal) === JSON.stringify(geometriaComplementarAlterada)){
									var feature = new ol.Feature({
								               geometry : new ol.geom.Polygon(geometriaComplementarAlterada)
								           });
								    benfeitoriaEspecificaLayerAzul.getSource().addFeature(feature);
									$scope.$emit('emitDesabilitarCheckboxMapaBenfeitoria', benfeitoriaAlterada);
								}

						}else if(geometriaEdificacaoAlterada != undefined){
								if(JSON.stringify(geometriaEdificacaoOriginal) === JSON.stringify(geometriaEdificacaoAlterada)){
									var feature = new ol.Feature({
								               geometry : new ol.geom.Polygon(geometriaEdificacaoAlterada)
								           });
								    benfeitoriaEspecificaLayerAzul.getSource().addFeature(feature);
								    $scope.$emit('emitDesabilitarCheckboxMapaBenfeitoria', benfeitoriaAlterada);
								}

								else if(JSON.stringify(geometriaEdificacaoOriginal) !== JSON.stringify(geometriaEdificacaoAlterada)){
									var feature = new ol.Feature({
								               geometry : new ol.geom.Polygon(geometriaEdificacaoAlterada)
								           });
								    benfeitoriaEspecificaLayer.getSource().addFeature(feature);
								    $scope.$emit('emitBenfeitoriaAlterada', benfeitoriaAlterada);
								}
						}



						benfeitoriaEspecificaLayer.setStyle(styleBenfeitoria);
						benfeitoriaEspecificaLayerAzul.setStyle(styleBenfeitoriaAzul);

						$(".mapaBenfeitoriaAlterada"+ContadorService.getContador()).css('border-style','solid');
						$(".mapaBenfeitoriaAlterada"+ContadorService.getContador()).css('border-color','red');





					if(ContadorService.getContador() > vm.imovelAlterado.benfeitorias.length-1){
						ContadorService.setContador(0);
					}else{
						ContadorService.setContador(ContadorService.getContador()+1);
					}

					if(terreno != undefined){
						map.addLayer(terrenoLayer);
					}
					if(terrenoCartorial != undefined){
							map.addLayer(terrenoCartorialLayer);
					}


					var elementos = angular.element( document.querySelector( '#overlay' ) );

	    			var elemento = elementos[0];

					var overlay = new ol.Overlay({
	            		element: elemento,
	            		stopEvent: false
	            	});

	    			map.addOverlay(overlay);

					map.on("click", function(evt){
	            		var feature = map.forEachFeatureAtPixel(evt.pixel, function(feature, layer){
							return feature;
						});
						if(feature){
							var coordenadas = feature.getGeometry().getCoordinates();
							overlay.setPosition(evt.coordinate);
							elemento.setAttribute("style", "display:inline; height:90px;");
						}else{
							overlay.setPosition(undefined);
						}

						feature = "";

	            	});

					map.addLayer(benfeitoriasLayer);
					map.addLayer(benfeitoriaEspecificaLayer);
					map.addLayer(benfeitoriaEspecificaLayerAzul);
					map.addLayer(unidadeAutonomaLayer);
					map.addLayer(pontoLayer);

					if(terreno != undefined){
						var terrenoExtent = terrenoLayer.getSource().getExtent();
									map.getView().fit(terrenoExtent,map.getSize());

									view.setCenter(map.getView().getCenter());
						}else{
							var terrenoExtent = terrenoCartorialLayer.getSource().getExtent();
										map.getView().fit(terrenoExtent,map.getSize());

										view.setCenter(map.getView().getCenter());
						}

				});
			}
		});


	}



})();

(function() {
    'use strict';
    selecionarRipController.$inject = ["$scope", "$mdDialog", "Dominio", "UfsService", "PaisesUtilizadosService", "MunicipioService", "PesquisaRipsService", "ectCepService", "CidadesExtService", "PesquisaRIPsNaoPaginado", "multipleSelection", "imovel", "MensagensService", "$state", "PaginacaoFactory", "$window"];
    angular
        .module('su-cadastro-imovel')
        .controller('SelecionarRipController', selecionarRipController);

    function selecionarRipController(
        $scope,
        $mdDialog,
        Dominio,
        UfsService,
        PaisesUtilizadosService,
        MunicipioService,
        PesquisaRipsService,
        ectCepService,
        CidadesExtService,
        PesquisaRIPsNaoPaginado,
        multipleSelection,
        imovel,
        MensagensService,
        $state,
        PaginacaoFactory,
        $window
    ) {
        var vm = $scope;

        vm.multipleSelection = multipleSelection;
        vm.paises = PaisesUtilizadosService.query();

        vm.ufs = UfsService.UFS;
        vm.municipios = [];
        vm.isCEPPreenchido = false;
        vm.RIPSSelecionados = [];
        vm.resultadoPesquisa = {};
        vm.selected = {};
        vm.consulta;
        vm.isCepGenerico = false;
        vm.paginacao = PaginacaoFactory.getDefault();

        vm.limpar = function() {
            vm.filtroRIPs = {};
            vm.filtroRIPs.enderecamento = {};
            vm.filtroRIPs.enderecamento.pais = Dominio.Constantes.Brasil;
        };

        vm.limpar(); //inicializando o país

        vm.fechar = function() {
            $mdDialog.cancel();
        };

        vm.buscarMunicipiosPorUf = function() {

            if (
                vm.filtroRIPs.enderecamento.uf &&
                vm.filtroRIPs.enderecamento.uf.sigla
            ) {
                vm.municipios = [];
                vm.filtroRIPs.enderecamento.municipio = undefined;

                vm.municipios = MunicipioService.TodosMunicipios.query({
                    uf: vm.filtroRIPs.enderecamento.uf.sigla,
                });

                vm.filtroRIPs.enderecamento.uf = vm.filtroRIPs.enderecamento.uf.sigla;
            }
        };

        vm.selecionaMunicipio = function() {

            if (
                vm.filtroRIPs.enderecamento.municipio &&
                vm.filtroRIPs.enderecamento.municipio.nome
            ) {

                vm.filtroRIPs.enderecamento.municipio =
                    vm.filtroRIPs.enderecamento.municipio.nome;
            }
        };

        vm.pesquisar = function() {
            if (vm.filtroRIPs.enderecamento.municipio === '') {
                vm.filtroRIPs.enderecamento.municipio = null;
            }
            if (vm.filtroRIPs.enderecamento.geometria) {
                vm.filtroRIPs.enderecamento.geometria = {};
            }
            var filtroSerializado = angular.toJson(vm.filtroRIPs);

            PesquisaRipsService.query({
                imovelPesquisa: filtroSerializado,
                page: vm.paginacao.page,
                max: vm.paginacao.limit,
            }).$promise.then(function(data) {
                if (data.content.length > 0) {
                    vm.resultadoPesquisa = data.content;
                    vm.paginacao.totalElements = data.totalElements;
                } else {
                    MensagensService.exibirMensagemErro('Nenhum RIP encontrado');
                    vm.resultadoPesquisa = [];
                }
            });
        };

        vm.buscarEnderecoCep = function() {
            var cep = vm.filtroRIPs.enderecamento.cep;
            if (cep != undefined && cep.length == 8) {
                ectCepService.get({ cep: cep }).$promise.then(function(data) {
                    if (data != undefined) {
                        data.pais = Dominio.Constantes.Brasil;
                        if (vm.municipios.length > 0) {
                            vm.municipios = [];
                        }
                        vm.municipios.push(data.municipio);
                        vm.filtroRIPs.enderecamento = data;

                        if (vm.verificaCEPGenerico(cep)) {
                            vm.isCepGenerico = true;
                        } else {
                            vm.isCepGenerico = false;
                        }

                        vm.isCEPPreenchido = true;
                    } else {
                        MensagensService.exibirMensagemErro('CEP não encontrado');
                    }
                });
            } else {
                vm.isCEPPreenchido = false;
                vm.filtroRIPs.enderecamento.uf = undefined;
                vm.filtroRIPs.enderecamento.municipio = undefined;
            }
        };

        vm.page = function() {
            if (vm.filtroRIPs.enderecamento.municipio === '') {
                vm.filtroRIPs.enderecamento.municipio = null;
            }
            if (vm.filtroRIPs.enderecamento.geometria) {
                vm.filtroRIPs.enderecamento.geometria = {};
            }
            var filtroSerializado = angular.toJson(vm.filtroRIPs);
            PesquisaRipsService.query({
                imovelPesquisa: filtroSerializado,
                page: vm.paginacao.page,
                max: vm.paginacao.limit,
            }).$promise.then(function(data) {
                if (vm.RIPSSelecionados.length > 0) {
                    angular.forEach(vm.RIPSSelecionados, function(value) {
                        angular.forEach(data.content, function(valueData) {
                            if (value === valueData.rip) {
                                valueData.isSelecionado = true;
                            }
                        });
                    });
                }

                vm.resultadoPesquisa = data.content;
                if (vm.selecionarTodos) {
                    vm.marcarDesmarcarTodosCheckbox();
                }
            });
        };

        vm.atualizaMarcarTodosCheckbox = function(item) {
            if (!item.isSelecionado) {
                if (vm.selecionarTodos) {
                    vm.selecionarTodos = false;
                }
                angular.forEach(vm.RIPSSelecionados, function(value, key) {
                    if (value === item.rip) {
                        vm.RIPSSelecionados.splice(key, 1);
                    }
                });
            } else {
                var ripNaoSelecionado = true;
                angular.forEach(vm.RIPSSelecionados, function(value) {
                    if (value === item.rip) {
                        ripNaoSelecionado = false;
                    }
                });
                if (ripNaoSelecionado) {
                    vm.RIPSSelecionados.push(item.rip);
                }
            }
        };

        vm.apresentarRipsMarcardos = function(rips) {
            vm.consultaVarios = [];
            vm.consulta = '';
            angular.forEach(rips, function(rip) {
                vm.consulta += vm.consulta.length > 0 ? ', ' + rip : rip;
            });
        };

        vm.confirmar = function() {
            if (!vm.selecionarTodos) {
                if (vm.RIPSSelecionados.length === 0) {
                    MensagensService.exibirMensagemErro(
                        'Necessário selecionar um imóvel.'
                    );
                } else {
                    vm.apresentarRipsMarcardos(vm.RIPSSelecionados);
                    $mdDialog.hide(vm.consulta);
                }
            } else {
                var filtroSerializado = angular.toJson(vm.filtroRIPs);
                PesquisaRIPsNaoPaginado.query({
                    imovelPesquisa: filtroSerializado,
                }).$promise.then(function(data) {
                    angular.forEach(data, function(value) {
                        var isItemSelecionado = false;
                        angular.forEach(vm.RIPSSelecionados, function(valueSelecionado) {
                            if (valueSelecionado === value.rip) {
                                isItemSelecionado = true;
                            }
                        });
                        if (!isItemSelecionado) {
                            vm.RIPSSelecionados.push(value.rip);
                        }
                    });
                    vm.apresentarRipsMarcardos(vm.RIPSSelecionados);
                    $mdDialog.hide(vm.consulta);
                });
            }
        };

        vm.selecionarRip = function() {
            if (vm.selected.id) {
                imovel.incorporacao.codigoOrigem = vm.selected.rip;
                $mdDialog.hide();
            } else {
                MensagensService.exibirMensagemErro('Necessário selecionar um imóvel.');
            }
        };

        vm.verificaCEPGenerico = function(cep) {
            return cep.endsWith('000');
        };

        vm.marcarDesmarcarTodosCheckbox = function() {
            if (vm.selecionarTodos) {
                angular.forEach(vm.resultadoPesquisa, function(item) {
                    var isItemSelecionado = false;
                    angular.forEach(vm.RIPSSelecionados, function(itemSelecionado) {
                        if (itemSelecionado === item.rip) {
                            isItemSelecionado = true;
                        }
                    });
                    if (!isItemSelecionado) {
                        vm.RIPSSelecionados.push(item.rip);
                    }
                    item.isSelecionado = true;
                });
            } else {
                vm.RIPSSelecionados = [];
                angular.forEach(vm.resultadoPesquisa, function(item) {
                    item.isSelecionado = false;
                });
            }
        };

        vm.irParaDetalhamento = function(idImovel) {
            var url = $state.href('INCORPORACAO.detalharImovel', { id: idImovel });
            $window.open(url, '_blank');
        };

        vm.buscarCidadesPorPais = function() {
          vm.filtroRIPs.enderecamento.cidadeExterior = null;
            if (vm.filtroRIPs.enderecamento.pais.id == 1) {
                vm.cidades = [];
            } else {
                vm.cidades = CidadesExtService.query({
                    id_pais: vm.filtroRIPs.enderecamento.pais.id,
                });
            }
        };
    }
})();

(function () {
    "use strict";
    naoSeiRip.$inject = ["$mdDialog", "$document"];
    angular.module("su-cadastro-imovel").directive("naoSeiRip", naoSeiRip);

    function naoSeiRip($mdDialog, $document) {
        return {
            template: '<a ng-click="abrirModalConsultaRips()" ui-sref="">{{text}}</a>',
            restrict: 'E',
            scope: {
                multipleSelection: '=multipleSelection',
                text: '@',
                imovel:'=',
                consulta:'='
            },
            link: function (scope) {
                var vm = scope, multiple = vm.multipleSelection;

                vm.abrirModalConsultaRips = function () {

                 vm.ripSelecionado = $mdDialog.show({
                     controllerAs:'vm',
                     controller: 'SelecionarRipController',
                     templateUrl: 'scripts-cadastro-imovel/app/directives/seleciona-rips-modal/view/seleciona-rip-template-modal.html',
                     parent: angular.element($document[0].body),
                     clickOutsideToClose: false,
                     locals : {
                         multipleSelection: multiple,
                         imovel: vm.imovel
                     }
                 }).then(function (result) {
                     vm.consulta = result;
                 });

                }


            }
        };
    }

}());

(function () {
    "use strict";
    directive.$inject = ["$mdDialog", "ArquivoService", "MensagensService", "$document", "PaginacaoFactory"];
    angular.module("su-cadastro-imovel").directive("uploadFotos", directive);

    function directive($mdDialog, ArquivoService, MensagensService, $document, PaginacaoFactory){
        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/upload-fotos/template/upload-fotos.html",
            scope: {
                model: "=",
                action: "@",
                desabilitar: "="
            },
            controller: function () {
                var vm = this;
                vm.today = new Date();
                vm.fotoFile = undefined;
            },
            controllerAs: "ctrl",
            link: function($scope, el, attrs, ctrl) {

                var vm = $scope,
                    isImagemFormatoValido,
                    isImagemTamanhoValido;

                vm.foto = {imagem: {}};
                vm.mostrarCampoArquivo = false;
                vm.ordenacao = null;
                vm.paginacao = PaginacaoFactory.getDefault();

                function init () {
                  gerarUrlImagens();
                }

                vm.adicionarFoto = function () {
                    if ($scope.forms.imagemForm.$valid) {

                        if (!isImagemFormatoValido()) {
                            MensagensService.exibirMensagemErro("Arquivo em formato inválido");
                        } else if (!isImagemTamanhoValido()) {
                            MensagensService.exibirMensagemErro("O arquivo não pode possuir mais de 5mb");
                        } else {
                            ArquivoService.uploadArquivo(ctrl.fotoFile).then(
                                function (response) {
                                    vm.foto.imagem.arquivo = response.data.byteArray;
                                    vm.foto.imagem.nomeOriginal = response.data.nome;
                                    vm.foto.imagem.tipoArquivo = response.data.tipo;
                                    vm.model.push(vm.foto);
                                    vm.foto = {imagem: {}};
                                    vm.mostrarCampoArquivo = false;
                                    ctrl.fotoFile = undefined;
                                    $scope.forms.imagemForm.$setPristine();
                                    $scope.forms.imagemForm.$setUntouched();
                                }
                            );
                        }

                    } else {
                        vm.mostrarCampoArquivo = true;
                        $scope.forms.imagemForm.$setSubmitted();
                        MensagensService.exibirMensagemCamposObrigatorios();
                    }
                };

                vm.excluirFoto = function (ev, foto) {
                    var confirm = $mdDialog.confirm({
                        focusOnOpen: false
                    })
                        .textContent("Deseja confirmar a exclusão da foto?")
                        .targetEvent(ev)
                        .cancel("Não")
                        .ok("Sim");

                    $mdDialog.show(confirm).then(
                        function () {
                            var index = vm.model.indexOf(foto);
                            vm.model.splice(index, 1);
                        }
                    );
                };

                isImagemFormatoValido = function () {
                    var formatosValidos = ["image/png", "image/jpeg", "image/bmp"];
                    return formatosValidos.includes(ctrl.fotoFile.type);
                };

                isImagemTamanhoValido = function () {
                    return ctrl.fotoFile.size <= 5000000;
                };

                vm.abrirModal = function(ev, index) {
                    var imagens = [];
                    angular.forEach($scope.model, function(value, key){
                        imagens.push($scope.model[key].imagem);
                    });
                    $mdDialog.show({
                        controller: ["$scope", "slides", "startIndex", function($scope, slides, startIndex){
                        $scope.slides = slides;
                        $scope.startIndex = startIndex;
                            $scope.fechar = function()  {
                                $mdDialog.cancel();
                            }
                        }],
                        controllerAs: 'showImagemModalCtrl',
                        templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos-detalhar-modal.html',
                        parent: angular.element($document.body),
                        targetEvent: ev,
                        clickOutsideToClose:true,
                        locals: {
                            slides: imagens,
                            startIndex: index
                        }
                    });
                };

                vm.isDetalhar = function  () {
                    return vm.action === 'detail' || vm.desabilitar;
                }

                function gerarUrlImagens() {
                  angular.forEach(vm.model, function (arquivo) {
                      if (arquivo.imagem && arquivo.imagem.arquivo) {
                        arquivo.imagem.url = ArquivoService.getUrlFromBase64(arquivo.imagem.arquivo);
                      }
                  });
                }

                init();
            }
        }
    }
})();

(function () {
  "use strict";
  directive.$inject = ["$mdDialog", "ArquivoService", "$window", "MensagensService", "CartorioService", "PaginacaoFactory"];
  angular.module("su-cadastro-imovel").directive("uploadArquivos", directive);

  function directive($mdDialog, ArquivoService, $window, MensagensService,  CartorioService, PaginacaoFactory){
      return {
          templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/upload-arquivos/template/upload-arquivos.html",
          scope: {
              model: "=",
              titulo: "@",
              imovel: "=",
              action: "@",
              desabilitar: "="
          },
          controller: function () {
              var vm = this;
              vm.file = undefined;
              vm.today = new Date();

          },
          controllerAs: "ctrl",
          link: function($scope, el, attrs, ctrl) {

              var vm = $scope,
                  base64ToArrayBuffer,
                  gerarUrlDocumento;

              vm.documento = {arquivo: {}};
              vm.mostrarCampoArquivo = false;
              vm.ordenacao = null;
              vm.paginacao = PaginacaoFactory.getDefault();
              vm.cartorios = [];

              function init () {
                  vm.matricula = {};
                  angular.forEach(vm.model, function (documento) {
                      gerarUrlDocumento(documento);
                  });

                  if(vm.imovel && vm.cartorios.length == 0 && vm.isAlteracao()){
                    //obterCartorio(vm.imovel.incorporacao.matriculas[0].cartorioID)
                    obterCartorios(vm.imovel.incorporacao.matriculas);
                  }
              }

              vm.adicionarDocumento = function () {
                  if ($scope.forms.documentosForm.$valid) {
                      ArquivoService.uploadArquivo(ctrl.file).then(
                          function (response) {
                              vm.documento.arquivo.arquivo = response.data.byteArray;
                              vm.documento.arquivo.nomeOriginal = response.data.nome;
                              vm.documento.arquivo.tipoArquivo = response.data.tipo;
                              gerarUrlDocumento(vm.documento);
                              vm.model.push(vm.documento);
                              vm.limparDocumentosForm();
                          }
                      );
                  } else {
                      vm.mostrarCampoArquivo = true;
                      $scope.forms.documentosForm.$setSubmitted();
                      MensagensService.exibirMensagemCamposObrigatorios();
                  }
              };

              vm.excluirDocumento = function (ev, documento) {
                  var confirm = $mdDialog.confirm({
                      focusOnOpen: false
                  })
                      .textContent("Tem certeza que deseja excluir este arquivo?")
                      .targetEvent(ev)
                      .cancel("Não")
                      .ok("Sim");

                  $mdDialog.show(confirm).then(
                      function () {
                          var index = vm.model.indexOf(documento);
                          vm.model.splice(index, 1);
                      }
                  );
              };

              /**
               * @property {object} documentosForm - Formulário de documentos.
               */
              vm.limparDocumentosForm = function () {
                  vm.documento = {arquivo: {}};
                  ctrl.file = undefined;
                  vm.mostrarCampoArquivo = false;
                  vm.forms.documentosForm.$setPristine();
                  vm.forms.documentosForm.$setUntouched();
              };

              base64ToArrayBuffer = function (base64) {
                   var binaryString = $window.atob(base64);
                   var binaryLen = binaryString.length;
                   var bytes = new Uint8Array(binaryLen);
                   for (var i = 0; i < binaryLen; i++) {
                       var ascii = binaryString.charCodeAt(i);
                       bytes[i] = ascii;
                   }
                   return bytes;
              };

              gerarUrlDocumento = function (documento) {
                  if (documento.arquivo && documento.arquivo.arquivo) {
                      var url = $window.URL || $window.webkitURL;
                      var blob = new Blob([base64ToArrayBuffer(documento.arquivo.arquivo)], {type: 'application/octet-stream'});
                      documento.url = url.createObjectURL(blob);
                  }
              };

              vm.getNomeCartorio = function (id) {
                  if (vm.imovel) {
                      var matriculas = vm.imovel.incorporacao.matriculas;
                      for (var i = 0; i < matriculas.length; i++) {
                          if (matriculas[i].cartorio && matriculas[i].cartorio.id == id) {
                              return matriculas[i].cartorio.nome;
                          }
                      }
                  }
              };

              var obterCartorios = function(matriculas) {
                matriculas.forEach(function (matricula) {
                    obterCartorio(matricula.cartorioID);
                });
              }

              var obterCartorio = function(cartorioID) {
                CartorioService.pesquisaCartorioPorId
                      .query({ id: cartorioID })
                      .$promise.then(function(data) {
                        if(!vm.cartorios.some(function(e){
                            return e.id === cartorioID; })){ // não repetir nomes de cartórios
                            vm.cartorios.push(data)
                        }
                        return;
                      });
              };

              vm.isDetalhar = function() {
                  return vm.action === 'detail' || vm.desabilitar;
              };

              vm.isAlteracao = function() {
                return vm.action === 'edit';
            };

              vm.$watchCollection(
                  "imovel.incorporacao.matriculas",
                  function(matriculas) {
                      angular.forEach(matriculas, function (matricula) {
                          if (matricula.cartorio && !hasCartorio(matricula.cartorio)) {
                              vm.cartorios.push(matricula.cartorio);
                          }
                      });
                      checaRemoveCartorio(matriculas);

                  });

              function hasCartorio(cartorio) {
                if (vm.cartorios!= null && vm.cartorios.length >0){
                  for (var i = 0; i < vm.cartorios.length; i++) {
                      if (angular.isDefined(vm.cartorios[i]) && vm.cartorios[i].id === cartorio.id) {
                          return true;
                      }
                  }
                }
                return false;
              }
              function checaRemoveCartorio(matriculas) {
                for (var i = vm.cartorios.length-1; i >= 0; i--) {
                    if(!matriculas.some(function(matricula){
                        return matricula.cartorio ? matricula.cartorio.id == vm.cartorios[i].id : true; })){
                            vm.cartorios.splice(i, 1); // se nenhuma matricula conter esse cartorio, deletar da lista
                    }
                }
              }

              init();
          }
      }
  }
})();

(function () {
    "use strict";
    /*global ol*/
    directive.$inject = ["ciMapaStyleFactory", "MensagensService", "$rootScope", "NotificadorService", "MAPA_CONSTANTS_INCORPORACAO"];
    angular.module("su-cadastro-imovel").directive("selecaoEdificacaoUnidadeAutonoma", directive);

    function directive(ciMapaStyleFactory, MensagensService, $rootScope,NotificadorService,MAPA_CONSTANTS_INCORPORACAO) {
        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/unidade-autonoma/templates/selecao-edificacao-unidade-autonoma.html",
            scope: {
                edificacao: "=",
                condicoesAcessibilidade: "=",
                terreno: "=?",
                edificacoes: "=?",
                onVoltar: "&"
            },
            link: function ($scope, element) {
                angular.element(document).ready(function () {
                    var vm = $scope,
                        terreno,
                        geoJson = new ol.format.GeoJSON(),
                        view,
                        osm,
                        terrenoSource,
                        terrenoLayer,
                        edificacaoSource,
                        edificacaoLayer,
                        mapaElement,
                        overlay,
                        overlayElement,
                        edificacaoSelecionada,
                        notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES;

                    function init() {

                        criarFeatures();

                        criarTarget();

                        vm.map = new ol.Map({
                            layers: [osm, terrenoLayer, edificacaoLayer],
                            target: mapaElement,
                            view: view
                        });

                        $rootScope.$broadcast('carregaterreno',{ any: vm.map});

                        if (terreno !=null && vm.map != null){
                          vm.map.getView().fit(terreno.getExtent(), vm.map.getSize());
                        }

                        removeInteractions();

                        criarEventoSelect();

                        criarEventoPointermove();

                    }

                    function initEditar() {
                        criarFeatures();
                    }

                    view = new ol.View({
                        center: [0,0],
                        zoom: 10
                    });

                    osm = new ol.layer.Tile({
                        source: new ol.source.OSM()
                    });

                    terrenoSource = new ol.source.Vector();

                    terrenoLayer = new ol.layer.Vector({
                        source: terrenoSource,
                        name: "terrenoLayer"
                    });

                    edificacaoSource = new ol.source.Vector();

                    edificacaoLayer = new ol.layer.Vector({
                        source: edificacaoSource,
                        name: "edificacaoLayer"
                    });

                    terreno = vm.terreno.geometriaTerrenoCartorial || vm.terreno.geometriaTerreno;

                    terreno = geoJson.readGeometry(terreno);

                    function criarTarget() {
                        var parent;

                        parent = element[0].firstChild;
                        mapaElement = angular.element("<div></div>")[0];
                        mapaElement.setAttribute("style", "height: 60vh; padding: 15px;");

                        parent.insertBefore(mapaElement, parent.children[1]);
                    }

                    function criarFeatures() {

                        var featureTerreno = new ol.Feature({
                            geometry: terreno
                        });
                        featureTerreno.setStyle(ciMapaStyleFactory.terrenoVerde());
                        terrenoSource.addFeature(featureTerreno);
                        //Edificações vem vazia, está desenhando no ci-mapa
                        if (vm.edificacoes && vm.edificacoes.length > 0) {
                            angular.forEach(vm.edificacoes, function (dados) {

                                var featureEdificacao = new ol.Feature({
                                    geometry: geoJson.readGeometry(dados.edificacao.geometria)
                                });
                                featureEdificacao.setId(dados.edificacao.id);
                                featureEdificacao.set("dadosEdificacao", dados.edificacao);
                                featureEdificacao.set("condicoesAcessibilidade", dados.condicoesAcessibilidade);
                                featureEdificacao.setStyle(ciMapaStyleFactory.terrenoVerde());
                                edificacaoSource.addFeature(featureEdificacao);
                            });
                        }
                    }

                    function removeInteractions() {
                        vm.map.getInteractions().clear();
                    }

                    var uudiEdificacaoSelecionada;

                    function criarEventoSelect() {
                        var select = new ol.interaction.Select({
                            layers: function (layer) {
                                return layer.get("name") === "edificacaoLayer";
                            }
                        });

                        vm.map.addInteraction(select);

                        select.on("select", function (event) {

                          $rootScope.$broadcast('deselecionarEdificacoes',{});

                            if (event.selected.length > 0) {
                                edificacaoSelecionada = {
                                    dadosEdificacao: event.selected[0].get("dadosEdificacao"),
                                    condicoesAcessibilidade: event.selected[0].get("condicoesAcessibilidade")
                                };
                                event.selected[0].setStyle(ciMapaStyleFactory.terrenoAmarelo());
                                uudiEdificacaoSelecionada = event.selected[0].get("dadosEdificacao").uuid;
                            } else {
                                edificacaoSelecionada = null;
                            }
                        });
                    }

                    function criarEventoPointermove() {
                        setOverlay();
                        vm.map.on("pointermove", function (event) {
                            var feature = vm.map.forEachFeatureAtPixel(event.pixel, function (feature) {
                                    return feature;
                                }, null,
                                function (layer) {
                                    return layer.get("name") === "edificacaoLayer";
                                }
                            );

                            if (feature && feature.getId()) {
                                var dados = feature.get("dadosEdificacao");
                                overlayElement.innerHTML = getConteudoPopup(dados);
                                overlay.setPosition(event.coordinate);
                                vm.map.getTarget().style.cursor = "pointer";
                            } else {
                                overlay.setPosition(undefined);
                                vm.map.getTarget().style.cursor = "default";
                            }
                        });
                    }

                    function setOverlay() {
                        overlayElement = angular.element("<div class='popup-mapa-bottom-left'></div>")[0];

                        overlay = new ol.Overlay({
                            element: overlayElement,
                            positioning: "bottom-left"
                        });

                        vm.map.addOverlay(overlay);
                    }

                    function getConteudoPopup(dados) {
                      if (dados != undefined){
                        return "<div class='layout-column'>" +
                                "<div class='layout-row'><b>Nome:</b>&nbsp;"+ dados.nome +"</div>" +
                                "<div class='layout-row'><b>Tipo:</b>&nbsp;"+ (dados.tipoEdificacao != undefined ? dados.tipoEdificacao.descricao: '') +"</div>" +
                                "</div>";
                      } else {
                        return "Não encontrado"
                      }
                    }

                    vm.editarVoltar = function (){
                        $rootScope.$broadcast('editarVoltarUnidadeAutonoma');
                    }

                    $scope.$on('editHideAlterarUnidadeEdificacao', function(event, args) {
                        vm.edit = true;
                        vm.showAlterarUnidadeEdificio = true;
                    });

                    vm.confirmarSelecao = function () {
                        if (edificacaoSelecionada) {
                            vm.edificacao = edificacaoSelecionada.dadosEdificacao;
                            vm.condicoesAcessibilidade = edificacaoSelecionada.condicoesAcessibilidade;
                            vm.documentacao =  vm.edificacao.documentacao;
                            $rootScope.$broadcast('atualizaEdificacaoUnidadeAutonoma',{ uudiEdificacaoSelecionada: uudiEdificacaoSelecionada });
                            $rootScope.$broadcast('confirmaSelecaoEdificacao',{ edificacao: vm.edificacao });
                            $rootScope.$broadcast('atualizaCondicoesAcessibUnidadeAutonoma',{ condicoesAcessibilidade: vm.condicoesAcessibilidade });
                            $rootScope.$broadcast('atualizaDocumentacaoUnidadeAutonoma',{ documentacao: vm.documentacao });
                            $rootScope.$broadcast('editarVoltarUnidadeAutonoma');
                            NotificadorService.notify($scope, true, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                        } else {
                            MensagensService.exibirMensagemErro("Necessário selecionar uma edificação");
                        }
                    };

                    init();
                });
            }
        };
    }
})();

(function () {
    "use strict";
    directive.$inject = ["$mdDialog", "$filter", "$rootScope", "NotificadorService", "Dominio", "MAPA_CONSTANTS_INCORPORACAO", "ValidaAreaTerrenoService", "ValidaFracaoIdealTerrenoService", "MensagensService"];
    angular.module("su-cadastro-imovel").directive("dadosUnidadeAutonoma", directive);

    function directive($mdDialog, $filter, $rootScope, NotificadorService, Dominio, MAPA_CONSTANTS_INCORPORACAO, ValidaAreaTerrenoService, ValidaFracaoIdealTerrenoService,  MensagensService){
        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/unidade-autonoma/templates/dados-unidade-autonoma.html",
            scope: {
                imovel: "=",
                unidadeAutonoma: "=",
                tipoImovel: "=",
                action: "=",
                desabilitar: "="
            },
            link: function($scope) {
                var vm = $scope;

                vm.isUnidadeAutonomaComGaragem = function () {
                    return vm.unidadeAutonoma.possuiGaragem === "Sim";
                };

                vm.showNumeroDormitorios = function () {
                    if(vm.unidadeAutonoma.edificacao != null){
                        return vm.unidadeAutonoma.edificacao.tipoProjeto && vm.unidadeAutonoma.edificacao.tipoProjeto.id === 2;
                    }
                };

                vm.isApartamento = function () {
                    return vm.tipoImovel.id === 7;
                };

                vm.dadosUnidadeAutonomaDisabled = function () {
                    return isDetalhamento() || vm.desabilitar;
                };

                function isDetalhamento() {
                    return vm.action === 'detail';
                }

                vm.isImovelUnidadeAutonoma = function () {
                    return vm.tipoImovel && vm.tipoImovel.tipoPai === "UNIDADE AUTÔNOMA";
                };

                vm.validarFracaoIdealImovel = function () {
                    if (vm.imovel.fracaoIdealImovel) {
                        if (vm.unidadeAutonoma && Number(vm.imovel.fracaoIdealImovel.replace(',', '.')) === 0) {

                            vm.imovel.fracaoIdealImovel = undefined;
                            MensagensService.exibirMensagemErro("A Fração Ideal do Imóvel deve ser maior que 0.");
                        } else {

                            if (vm.unidadeAutonoma.fracaoIdealCartorial && vm.terreno.areaTotalCartorial) {

                                if (vm.terreno.areaTotalCartorial < vm.unidadeAutonoma.fracaoIdealCartorial) {

                                    vm.unidadeAutonoma.fracaoIdealCartorial = undefined;
                                    MensagensService.exibirMensagemErro("A Fração Ideal do Imóvel deve ser inferior à Área Total.");
                                } else if (vm.isImovelUnidadeAutonoma() && vm.isImovelUniaoParcialmente()) {

                                    if (vm.unidadeAutonoma.fracaoIdealCartorial >= vm.terreno.areaUniaoCartorial) {

                                        vm.unidadeAutonoma.fracaoIdealCartorial = undefined;
                                        MensagensService.exibirMensagemErro("A Fração Ideal do Imóvel deve ser inferior à Área da União.");
                                    }
                                }
                            }

                            if (vm.imovel.terreno.id) {
                                ValidaFracaoIdealTerrenoService.get({
                                    idTerreno: vm.imovel.terreno.id,
                                    ripImovel: vm.imovel.rip,
                                    area: vm.imovel.fracaoIdealImovel
                                }).$promise.then(function (response) {
                                    if (!response.isValid) {
                                        vm.imovel.fracaoIdealImovel = undefined;
                                        MensagensService.exibirMensagemErro("A soma das frações ideias das unidades autônomas desse terreno é superior a área do terreno. Favor informar outro valor de Fração Ideal do Imóvel.");
                                    }
                                });
                            }
                        }
                        vm.imovel.fracaoIdealImovel = vm.imovel.fracaoIdealImovel.replace(',', '.');
                        vm.imovel.fracaoIdealImovel = Number(vm.imovel.fracaoIdealImovel).toFixed(9);
                        vm.imovel.fracaoIdealImovel = String(vm.imovel.fracaoIdealImovel).replace('.', ',');
                    }
                };

                vm.formatarNumero = function(fracaoIdealImovel){
                    if (fracaoIdealImovel) {
                        var stringNumero = fracaoIdealImovel.replace(/[^\d]+/g, ''),
                            inteiro = stringNumero.slice(0, 1),
                            decimal = stringNumero.slice(1, stringNumero.length);

                        if (fracaoIdealImovel.length !== 1) {
                            vm.imovel.fracaoIdealImovel = inteiro + ',' + decimal;
                        } else {
                            vm.imovel.fracaoIdealImovel = fracaoIdealImovel.replace(/[^\d]+/g, '');
                        }

                        if (fracaoIdealImovel === '1') {
                            vm.imovel.fracaoIdealImovel = '1,000000000'
                        } else if (fracaoIdealImovel === '1,00000000') {
                            vm.imovel.fracaoIdealImovel = undefined
                        } else if (vm.imovel.fracaoIdealImovel > 1) {
                            vm.imovel.fracaoIdealImovel = undefined;
                            MensagensService.exibirMensagemErro("A Fração Ideal do Imóvel deve ser inferior à Área Total.");
                        }
                    }
                };

                var unidade;
                vm.calculaFracaoAreaTotal = function() {
                    var fracaoNum;

                    if (vm.imovel.terreno.areaTotalCadastral && vm.imovel.fracaoIdealImovel) {
                        if (vm.imovel.terreno.unidadeMedidaCadastral === 'm2') {
                            unidade = 'm²: '
                        } else {
                            unidade = 'hc: '
                        }
                        vm.imovel.fracaoIdealImovel = String(vm.imovel.fracaoIdealImovel);
                        fracaoNum = vm.imovel.fracaoIdealImovel.replace(',', '.');
                        vm.imovel.fracaoAreaTotal = vm.imovel.terreno.areaTotalCadastral * fracaoNum;
                        vm.imovel.fracaoAreaTotal = String(vm.imovel.fracaoAreaTotal.toFixed(2));
                        vm.imovel.fracaoAreaTotal = unidade + vm.imovel.fracaoAreaTotal.replace('.', ',')
                    }
                };
                vm.calculaFracaoAreaTotal();

                vm.calculaFracaoAreaUniao = function() {
                    var fracaoNum;

                    if (vm.imovel.terreno.areaUniaoCadastral && vm.imovel.fracaoIdealImovel) {
                        if (vm.imovel.terreno.unidadeMedidaCadastral === 'm2') {
                            unidade = 'm²: '
                        } else {
                            unidade = 'hc: '
                        }
                        vm.imovel.fracaoIdealImovel = String(vm.imovel.fracaoIdealImovel);
                        fracaoNum = vm.imovel.fracaoIdealImovel.replace(',', '.');
                        vm.imovel.fracaoAreaUniao = vm.imovel.terreno.areaUniaoCadastral * fracaoNum;
                        vm.imovel.fracaoAreaUniao = String(vm.imovel.fracaoAreaUniao.toFixed(2));
                        vm.imovel.fracaoAreaUniao = unidade + vm.imovel.fracaoAreaUniao.replace('.', ',')
                    }
                };
                vm.calculaFracaoAreaUniao();

                vm.editandoUA = function(){
                  if (vm.imovel != null && vm.imovel !=undefined && vm.imovel.tipoImovel != null && vm.imovel.tipoImovel != undefined){
                    return vm.imovel.id !=null  && vm.isImovelUnidadeAutonoma();
                  }
                  return false;
                }
            }
        }
    }
})();

( function () {
    "use strict";
    directive.$inject = ["TipoGaragemService", "MensagensService", "$filter", "$rootScope"];
    angular.module("su-cadastro-imovel").directive("dadosGaragemUnidadeAutonoma", directive);

    function directive(TipoGaragemService,MensagensService,$filter,$rootScope) {
        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/unidade-autonoma/templates/dados-garagem-unidade-autonoma.html",
            scope: {
                unidadeAutonoma: "=",
                action: "=",
                desabilitar: "="
            },
            link: function ($scope) {

                var vm = $scope;
                vm.tiposGaragem = TipoGaragemService.query();

                vm.dadosGaragemDisabled = function () {
                  return isDetalhamento() || vm.desabilitar;
                };

                function isDetalhamento() {
                    return vm.action === 'detail';
                }

                vm.formatarNumero = function (fracaoIdeal) {
                  vm.unidadeAutonoma.fracaoIdealCadastral = fracaoIdeal.replace(
                    /[^\d]+/g,
                    ''
                  );
                  var stringNumero = fracaoIdeal.replace(/[^\d]+/g, '');
                  var inteiro = stringNumero.slice(0, 1);
                  var decimal = stringNumero.slice(1, stringNumero.length);
                  var numero = 0;
                  var decimalSeparator = '.';
                  var stringlSeparator = ',';

                  if (stringNumero.length > 1) {
                    numero = inteiro + decimalSeparator + decimal;
                  } else {
                    numero = stringNumero;
                  }

                  if (numero > 1) {
                    vm.unidadeAutonoma.fracaoIdealCadastral = undefined;
                  } else if (numero == 1 && numero.length == 1) {
                    var float = parseFloat(numero);
                    vm.unidadeAutonoma.fracaoIdealCadastral = $filter('number')(
                      float,
                      9
                    );
                    $scope.fracaoIdealCadastralNum = float.toFixed(9);
                    $rootScope.fracaoIdealCadastralNum = $scope.fracaoIdealCadastralNum;
                  } else if (numero[0] == 0 && numero.length > 1) {
                    vm.unidadeAutonoma.fracaoIdealCadastral =
                      inteiro + stringlSeparator + decimal;
                    $scope.fracaoIdealCadastralNum = parseFloat(
                      inteiro + decimalSeparator + decimal
                    );
                    $rootScope.fracaoIdealCadastralNum = $scope.fracaoIdealCadastralNum;
                  }
                };
          }
        }
    }
})();

(function() {
    "use strict";
    directive.$inject = ["TipoEdificacao", "TipoProjetoService", "$rootScope", "ArquivoService", "NotificadorService", "MAPA_CONSTANTS_INCORPORACAO", "Dominio", "MensagensService", "UnidadeAutonomaService", "$mdDialog"];
    angular.module("su-cadastro-imovel").directive("dadosEdificacaoUnidadeAutonoma", directive);

    function directive(TipoEdificacao, TipoProjetoService, $rootScope,
       ArquivoService, NotificadorService, MAPA_CONSTANTS_INCORPORACAO, Dominio, MensagensService, UnidadeAutonomaService, $mdDialog) {
        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/unidade-autonoma/templates/dados-edificacao-unidade-autonoma.html",
            scope: {
                unidadeAutonoma: "=",
                enderecamento: "=",
                formaCadastroEdificacao: "=",
                showTabFormDocumento: "=",
                documentoBackup: "=",
                documentos: "=",
                documento: "=",
                action: "=",
                desabilitar: "="
            },
            link: function($scope) {

                var vm = $scope,
                    notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES,
                    featureIds = MAPA_CONSTANTS_INCORPORACAO.FEATURE_ID;

                vm.jaConfirmou = false;

                vm.exibeTabFormDocumento = function(isExibeTabFormDocumento, isExibeTabInclusaoBenfeitoria, documento) {
                    vm.showTabFormDocumento = isExibeTabFormDocumento;
                    if (documento) {
                        if (!isEmpty(documento.dataEmissao)) {
                            documento.dataEmissao = toDate(documento.dataEmissao);
                        }

                        if (!isEmpty(documento.dataValidade)) {
                            documento.dataValidade = toDate(documento.dataValidade);
                        }
                        vm.documento = angular.copy(documento);
                        vm.documento.index = vm.unidadeAutonoma.edificacao.documentacao.indexOf(documento);
                        vm.documentoBackup = documento;
                    }
                };

                function toDate(dateStr) {
                    if (typeof dateStr != "string") {
                        return dateStr;
                    }
                    var parts = dateStr.split("/");
                    var date = parts[2] + "-" + parts[1] + "-" + parts[0] + " 00:00";
                    return new Date(date);
                }

                function isEmpty(value) {
                    return angular.equals({}, value) || angular.isUndefined(value) || value === null || value === "";
                }

                vm.TIPO_DESENHO = Dominio.Constantes.TiposDeDesenho;
                vm.tiposEdificacao = TipoEdificacao.query();
                vm.tiposProjeto = TipoProjetoService.query();

                vm.shapefileUnidadeAutonoma = null;
                vm.arquivoShape = null;
                vm.exibirBotao = true;
                vm.tipoCadastro = "";

                $scope.confirmarSelecaoTipoCadastro = function() {
                    if (!vm.tipoCadastro) {
                        vm.submittedUnidadeAutonoma = true;
                        MensagensService.exibirMensagemCamposObrigatorios();
                    }

                    if (vm.tipoCadastro === "EXISTENTE" || vm.tipoCadastro === "NOVA") {
                        vm.submittedUnidadeAutonoma = false;
                        vm.formaCadastroEdificacao = vm.tipoCadastro;
                    }

                    if (vm.tipoCadastro === "EXISTENTE") {
                        $rootScope.$broadcast('buscaEdificacoesPorTerreno');
                    }

                    if (vm.tipoCadastro === "NOVA") {
                        vm.selecionarCadastrarEdificacao();
                    }

                    if (vm.tipoCadastro === "NOVA" && vm.edit == true) {
                        $rootScope.$broadcast('editHabilitaDesenho');
                        zerarClearEdificacao();
                        vm.showSelecionarAlterarUnidadeEdificacao = false;
                        vm.showButtonselecionarCadastrarEdificacao = true;
                    }

                };
                vm.showAlterarUnidadeEdificio = false;
                vm.showButtonselecionarCadastrarEdificacao = true;

                $scope.$on('editHideAlterarUnidadeEdificacao', function(event, args) {
                    vm.edit = true;
                    vm.showAlterarUnidadeEdificio = true;
                });

                vm.selecionarCadastrarEdificacao = function() {
                    vm.showSelecionarAlterarUnidadeEdificacao = true;
                    vm.showButtonselecionarCadastrarEdificacao = false;
                }

                vm.exibeSelecionarUA = function() {
                  return (vm.tipoCadastro === "EXISTENTE");
                }

                vm.exibeNovaUA = function() {
                  return (vm.tipoCadastro === "NOVA");
                }

                vm.cancelarCadastrarEdificacao = function() {
                    vm.showSelecionarAlterarUnidadeEdificacao = false;
                    vm.showButtonselecionarCadastrarEdificacao = true;
                }

                $scope.$on('confirmaSelecaoEdificacao', function(event, args) {
                    var edificacao = args.edificacao;
                    vm.unidadeAutonoma.edificacao = edificacao;
                    vm.desabilitarCampos = true;
                    vm.showSelecionarAlterarUnidadeEdificacao = false;
                    vm.showButtonselecionarCadastrarEdificacao = false;
                    vm.showAlterarUnidadeEdificio = true;
                    vm.showSelecionarAlterarUnidadeEdificacao = true;
                    vm.geometriaTerrenoCadastralValida = true;
                    NotificadorService.notify(vm, { geometria: edificacao.geometria, isValid: true }, notifiers.GEOMETRIA_UNIDADE_AUTONOMA_ALTERADA);
                    $rootScope.$broadcast('marcarEdificacaoSelecionada',{uuid: edificacao.uuid});
                });

                $scope.$on('isEditartop', function(event, args) {
                    vm.showButtonselecionarCadastrarEdificacao = false;
                    vm.showAlterarUnidadeEdificio = true;
                    vm.showSelecionarAlterarUnidadeEdificacao = true;
                });

                vm.exibirCamposSelecaoTipoCadastro = function() {
                    return (vm.formaCadastroEdificacao !== "EXISTENTE" && vm.formaCadastroEdificacao !== "NOVA");
                };

                vm.exibirCampoPossuiElevador = function() {
                    if (vm.unidadeAutonoma.edificacao!=null && vm.unidadeAutonoma.edificacao.tipoEdificacao != undefined) {
                        return vm.unidadeAutonoma.edificacao.tipoEdificacao.id && vm.unidadeAutonoma.edificacao.tipoEdificacao.id !== 1;
                    } else {
                        return false;
                    }

                };

                vm.alterarTipoDesenho = function() {
                  if (vm.unidadeAutonoma.edificacao.tipoGeometria === vm.TIPO_DESENHO.VETORIZACAO) {
                     NotificadorService.notify($scope, null, "bloquearTelaUnidadeAutonoma");
                  }

                  if (angular.isDefined(vm.unidadeAutonoma.edificacao.geometria)) {
                      vm.shapefileUnidadeAutonoma = null;
                      vm.removerShapefile();
                  }
                };

                vm.alterarTipoCadastro = function() {
                  //zerarClearEdificacao();
                  //vm.unidadeAutonoma.edificacao.tipoGeometria = '';

                  $rootScope.$broadcast('exibirMapaSelecao',{tipo: vm.tipoCadastro});

                  vm.formaCadastroEdificacao = vm.tipoCadastro;

                  vm.confirmarSelecaoTipoCadastro();
                  if (vm.tipoCadastro === "NOVA"){
                    vm.desabilitarCampos = false;
                    vm.unidadeAutonoma = UnidadeAutonomaService.novaUnidadeAutonoma();
                  }

              };

                vm.uploadShapefile = function() {
                    if (vm.shapefileUnidadeAutonoma.name.endsWith(".zip")) {
                        ArquivoService.uploadShapefile(vm.shapefileUnidadeAutonoma, vm.enderecamento.codMunicipio).then(
                            function(response) {
                                vm.arquivoShape = response.data;
                                vm.unidadeAutonoma.edificacao.arquivoGeometria = {};
                                vm.unidadeAutonoma.edificacao.arquivoGeometria.arquivo = response.data.byteArray;
                                vm.unidadeAutonoma.edificacao.arquivoGeometria.nomeOriginal = response.data.nome;
                                vm.unidadeAutonoma.edificacao.arquivoGeometria.tipoArquivo = response.data.tipo;

                                vm.unidadeAutonoma.edificacao.geometria = response.data.geometria;
                                NotificadorService.notify($scope, vm.unidadeAutonoma.edificacao.geometria, notifiers.UPLOAD_SHAPEFILE_UNIDADE_AUTONOMA);
                                NotificadorService.notify(vm, { geometria: vm.unidadeAutonoma.edificacao.geometria, isValid: response.data != null }, notifiers.GEOMETRIA_UNIDADE_AUTONOMA_ALTERADA);
                            },
                            function(error) {
                                vm.shapefileUnidadeAutonoma = null;
                                vm.removerShapefile();
                                MensagensService.exibirMensagemErro(error.data.message);
                            }
                        );
                    } else {
                        vm.shapefileUnidadeAutonoma = null;
                        vm.removerShapefile();
                        MensagensService.exibirMensagemErro("Arquivo inválido");
                    }
                };

                function zerarClearEdificacao() {
                    vm.unidadeAutonoma.edificacao.arquivoGeometria = null;
                    vm.unidadeAutonoma.edificacao.dadosMedidor = [];
                    vm.unidadeAutonoma.edificacao.documentacao = [];
                    if (vm.unidadeAutonoma && vm.unidadeAutonoma.edificacao && vm.unidadeAutonoma.edificacao.geometria) {
                        //vm.unidadeAutonoma.edificacao.geometria.type = "";
                        delete vm.unidadeAutonoma.edificacao.geometria;
                    }
                    vm.unidadeAutonoma.edificacao.id = "";
                    vm.unidadeAutonoma.edificacao.nome = "";
                    vm.unidadeAutonoma.edificacao.observacoes = "";
                    vm.unidadeAutonoma.edificacao.possuiElevador = "";
                    vm.unidadeAutonoma.edificacao.tipoEdificacao = undefined;
                    vm.unidadeAutonoma.edificacao.descricao = "";
                    vm.unidadeAutonoma.edificacao.id = "";
                    vm.unidadeAutonoma.edificacao.ordem = "";
                    vm.unidadeAutonoma.edificacao.tipoGeometria = "";
                    if (vm.unidadeAutonoma.edificacao.tipoProjeto != undefined) {
                        vm.unidadeAutonoma.edificacao.tipoProjeto.codigoSistemico = "";
                        vm.unidadeAutonoma.edificacao.tipoProjeto.descricao = "";
                        vm.unidadeAutonoma.edificacao.tipoProjeto.id = "";
                    }
                    vm.unidadeAutonoma.edificacao.uuid = "";
                    vm.unidadeAutonoma.edificacao.possuiElevador = undefined;
                    vm.unidadeAutonoma.edificacao.tipoProjeto = undefined;

                    vm.desabilitarCampos = false;
                }

                vm.removerShapefile = function() {
                    vm.unidadeAutonoma.edificacao.geometria = undefined;
                    NotificadorService.notify($scope, featureIds.UNIDADE_AUTONOMA, notifiers.LIMPAR_DESENHO);
                };

                vm.dadosEdificacaoDisabled = function() {
                    return vm.isdetalhamento() || vm.desabilitarCampos || vm.desabilitar;
                };

                vm.getAcaoAcessibilidade = function(){
                  if (vm.desabilitarCampos) {
                     return 'detail';
                  } else {
                    return vm.action;
                  }
                }

                vm.isdetalhamento = function() {
                    return vm.action === 'detail';
                }

                vm.editandoUA = function(){

                  if(vm.edit == true && vm.unidadeAutonoma.id !=null){
                    return true;
                  }
                  return false;
                }

                vm.exibirMsgConfirmacaoEdicao = function(){
                  if (!vm.jaConfirmou && vm.unidadeAutonoma.id !=null && !vm.desabilitar){
                    var confirm = $mdDialog.alert({
                      focusOnOpen: false,
                      textContent: "A edição deste campo atualizará todos os RIPs de unidade autônoma na mesma edificação.",
                      ok: "Ok"
                    });
                    vm.jaConfirmou = true;
                    $mdDialog.show(confirm).then(function() {
                        //console.log("SIM")
                    }, function() {
                        //console.log("NÃO")
                    });
                  }
                }
            }
        }
    }
})();

(function () {
  'use strict';
  directive.$inject = ["TipoTituloAquisitivoService", "TipoAtoLegalService", "MoedaService", "ArquivoService", "MensagensService", "NotificadorService", "NOTIFICADORES"];
  angular.module('su-cadastro-imovel').directive('tituloAquisitivo', directive);

  function directive(
    TipoTituloAquisitivoService,
    TipoAtoLegalService,
    MoedaService,
    ArquivoService,
    MensagensService,NotificadorService,NOTIFICADORES
  ) {
    return {
      templateUrl:
        'scripts-cadastro-imovel/app/directives/secoes-formulario/titulo-aquisitivo/titulo-aquisitivo.html',
      scope: {
        titulo: '=',
        imovel: '=',
        showTituloAquisitivo: '=',
        tituloBackup: '=',
        queryPagination: '=',
        detail: '='

      },
      controller: function () {
        var vm = this;

        vm.arquivoFile = {};
        vm.arquivoFile.name = undefined;
      },
      controllerAs: 'ctrlTitulo',
      link: function ($scope, el, attrs, ctrlTitulo) {
        // VARIÁVEIS DA PÁGINA
        var vm = $scope;

        if (vm.titulo.nomeArquivo){
          ctrlTitulo.arquivoFilearquivoFile = {nomeOriginal: vm.titulo.nomeArquivo, arquivo: vm.titulo.arquivo}
        }

        vm.indexTitulo = undefined;
        vm.searchTipoTituloAquisitivo = undefined;
        vm.searchTipoAtoLegal = undefined;

        if (vm.imovel.titulos.includes(vm.tituloBackup)) {
          vm.indexTitulo = vm.imovel.titulos.indexOf(vm.tituloBackup);
          ctrlTitulo.arquivoFile.name =
            vm.imovel.titulos[vm.indexTitulo].nomeArquivo;
        }
        vm.ID_ATO_LEGAL = 4;
        vm.hoje = new Date();
        vm.titulos = [];

        vm.checkboxData = {
          valor: false,
        };

        // BANCO DE DADOS

        TipoTituloAquisitivoService.query().$promise.then(function (result) {
            vm.tiposTituloAquisitivo = result;
        });
        vm.tiposAtoLegal = TipoAtoLegalService.query();
        vm.moedas = MoedaService.query();

        // TÍTULO
        vm.cancelarInclusaoTitulo = function () {
          vm.showTituloAquisitivo = false;
          vm.limparInclusaoTitulo();
        };

        vm.isDetalhar = function () {
          return vm.detail;
        };

        /**
         * @property {object} inclusaoTituloForm - Formulário de inclusão de Título Aquisitivo.
         */
        vm.limparInclusaoTitulo = function () {
          vm.titulo = {
            tipoTituloAquisitivo: undefined,
            tipoAtoLegal: undefined,
            publicacao: {},
            moeda: {},
            encargos: [],
          };
          vm.tituloBackup = {};
          ctrlTitulo.arquivoFile = undefined;
          vm.checkboxData.valor = false;
          vm.forms.inclusaoTituloForm.$setPristine();
        };

        vm.limparArquivo = function () {
          NotificadorService.notify($scope, null, NOTIFICADORES.LIMPAR_ARQUIVO);
        }

        vm.objetosVaziosParaUndefined = function () {
          if (vm.titulo.tipoTituloAquisitivo.id != vm.ID_ATO_LEGAL) {
            vm.titulo.tipoAtoLegal = undefined;
            vm.titulo.publicacao = undefined;
          } else if (vm.titulo.publicacao.pagina == undefined) {
            vm.titulo.publicacao = undefined;
          }
          if (vm.titulo.valorContrato == undefined) {
            vm.titulo.moeda = undefined;
          }
        };

        vm.salvarTituloAquisitivo = function () {
          if (
            vm.imovel.incorporacao.doacaoComEncargo &&
            vm.titulo.encargos.length == 0 &&
            vm.titulo.tipoTituloAquisitivo.id != 4 &&
            vm.titulo.tipoTituloAquisitivo.id != undefined
          ) {
            MensagensService.exibirMensagemErro(
              'Necessário adicionar pelo menos um encargo'
            );

            return;
          }

          if (vm.forms.inclusaoTituloForm.$valid) {
            if (vm.indexTitulo != undefined) {
              vm.imovel.titulos[vm.indexTitulo] = angular.copy(vm.titulo);
            } else {
              vm.objetosVaziosParaUndefined();
              vm.imovel.titulos.push(vm.titulo);
            }

            vm.paginationTableFunctionTitulo();
            vm.limparInclusaoTitulo();
            vm.indexTitulo = undefined;
            vm.showTituloAquisitivo = false;
            MensagensService.exibirMensagemSucesso(
              'Título Aquisitivo adicionado'
            );

            NotificadorService.notify($scope,  vm.imovel.titulos, NOTIFICADORES.ADICIONAR_TITULO);

          } else if (vm.forms.inclusaoTituloForm.$error.required) {
            vm.forms.inclusaoTituloForm.$setSubmitted();
            MensagensService.exibirMensagemCamposObrigatorios();
          }
        };

        vm.uploadArquivo = function () {
          if (!isFormatoValido() || !isTamanhoValido()) {
            MensagensService.exibirMensagemErro('Arquivo inválido');
          } else {
            ArquivoService.uploadArquivo(ctrlTitulo.arquivoFilearquivoFile).then(function (
              data
            ) {
              vm.titulo.arquivo = data.data.byteArray;
              vm.titulo.nomeArquivo = data.data.nome;
            });
          }
        };

        /*vm.limparArquivo = function () {
          vm.titulo.arquivo = undefined;
          vm.titulo.nomeArquivo = undefined;
        };*/

        // FIM TÍTULO

        // VALIDAÇÕES TÍTULO

        //Tela já impede de colocar uma data de assinatura maior do que hoje
        vm.validarDataAssinatura = function () {
          if (vm.titulo.dataFinalVigencia != undefined) {
            if (vm.titulo.dataAssinatura >= vm.titulo.dataFinalVigencia) {
              MensagensService.exibirMensagemErro(
                'Data de assinatura inválida.'
              );
              vm.titulo.dataAssinatura = undefined;
            }
          }
          vm.dataMinFinal = new Date();
          vm.dataMinFinal.setDate(vm.titulo.dataAssinatura.getDate() + 1);
        };

        vm.validarDataFinal = function () {
          if (vm.titulo.dataProrrogacaoVigencia != undefined) {
            if (
              vm.titulo.dataFinalVigencia >= vm.titulo.dataProrrogacaoVigencia
            ) {
              MensagensService.exibirMensagemErro(
                'Data final da vigência inválida.'
              );
              vm.titulo.dataFinalVigencia = undefined;
              vm.titulo.dataProrrogacaoVigencia = undefined;
            }
          }
          vm.dateMinProrrogacao = new Date();
          vm.dateMinProrrogacao.setDate(
            vm.titulo.dataFinalVigencia.getDate() + 1
          );
        };

        vm.$watch('titulo.dataFinalVigencia', function () {
          if (vm.checkboxData.valor == true) {
            copiaDataFinalParaCumprimento();
          }
        });

        vm.$watch('titulo.tipoTituloAquisitivo', function () {

          if (!vm.titulo.tipoTituloAquisitivo || (vm.titulo.tipoTituloAquisitivo.id != vm.ID_ATO_LEGAL)) {
            vm.titulo.tipoAtoLegal = undefined;
          }
        });


        function isFormatoValido() {
          //DOC, PDF, DOCX, ODS, TXT
          var formatosValidos = [
            'application/msword',
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.oasis.opendocument.spreadsheet',
            'text/plain',
          ];
          return formatosValidos.includes(ctrlTitulo.arquivoFilearquivoFile.type);
        }

        function isTamanhoValido() {
          return ctrlTitulo.arquivoFilearquivoFile.size <= 5000000;
        }
        // FIM VALIDAÇÕES TÍTULO

        //Tabela Titulo

        vm.paginationTableFunctionTitulo = function () {
          var begin =
            (vm.queryPagination.query.page - 1) *
            vm.queryPagination.query.limit,
            end = begin + vm.queryPagination.query.limit;

          vm.queryPagination.listaResultante = vm.imovel.titulos.slice(
            begin,
            end
          );
        };


      }
    };
  }
})();

(function () {
    "use strict";
    restricoesTerreno.$inject = ["$mdDialog"];
    angular.module("su-cadastro-imovel").directive("restricoesUsoOcupacao", restricoesTerreno);

    function restricoesTerreno($mdDialog) {
        return {

            templateUrl:"scripts-cadastro-imovel/app/directives/secoes-formulario/restricoes-uso-ocupacao/templates/restricoes-uso-ocupacao.html",
            scope: {
                terreno:"=",
                isDetail:"=",
                idTipoImovel:"=",
                desabilitar:"="
            },
            link: function($scope) {

                var vm = $scope;
                vm.jaConfirmou = false;

                var ESPELHO_DAGUA = 3;
                var CAVIDADE_NATURAL = 5;
                var APARTAMENTO = 7;
                var GARAGEM = 8;
                var SALA = 9;

                vm.isEspelhoOuCavidade = function () {

                    return !(vm.idTipoImovel === ESPELHO_DAGUA || vm.idTipoImovel === CAVIDADE_NATURAL);

                };

                vm.exibirMsgConfirmacaoEdicao = function(){
                  if (vm.isImovelUnidadeAutonoma() && !vm.jaConfirmou && vm.terreno.id !=null){
                    var confirm = $mdDialog.alert({
                      focusOnOpen: false,
                      textContent: "A edição deste campo atualizará todos os RIPs de unidade autônoma no mesmo terreno. Todos os campos dessa seção serão atualizados.",
                      ok: "Ok"
                    });
                    vm.jaConfirmou = true;
                    $mdDialog.show(confirm).then(function() {
                    }, function() {
                    });
                  }
                }

                vm.isImovelUnidadeAutonoma = function () {
                  return (vm.idTipoImovel === APARTAMENTO || vm.idTipoImovel === GARAGEM || vm.idTipoImovel === SALA);
                };
            }

        }
    }

}());

(function () {
    "use strict";
    directive.$inject = ["TipoProprietarioReal", "Dominio", "ReceitaService", "imovelService", "$timeout", "ExtintasService", "$q", "NOTIFICADORES", "NotificadorService", "MensagensService", "CNPJ_UNIAO", "TIPO_PROPRIETARIO_UNIAO", "buscarCpfCnpjService", "cpfCnpjMaskService"];
    angular.module("su-cadastro-imovel").directive("dadosProprietario", directive);

    function directive(TipoProprietarioReal, Dominio, ReceitaService, imovelService,
        $timeout, ExtintasService, $q, NOTIFICADORES, NotificadorService, MensagensService, CNPJ_UNIAO, TIPO_PROPRIETARIO_UNIAO, buscarCpfCnpjService, cpfCnpjMaskService) {

        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/proprietario/template/dados-proprietario.html",
            restrict: 'E',
            scope: {
                imovel: "=",
                isDetail: "=",
                desabilitar: "="
            },
            controller: ["$rootScope", "$timeout", function ($rootScope, $timeout) {
                var vm = this;
                if ($rootScope.usuarioLogado) {
                    var unidadeExec = $rootScope.usuarioLogado.unidadeExecutoraPadrao;
                    vm.codigoUGUsuarioLogado = unidadeExec ? unidadeExec.codigoUg : $rootScope.usuarioLogado.idOrganizacao;
                    vm.ugUsuarioLogado = $rootScope.usuarioLogado.organizacao;
                }
                vm.ugProprietario = undefined;
                vm.ugProprietarioParcial = undefined;
                vm.ugProprietarioTitular = undefined;
                vm.orgaoEntidadeProprietario = undefined;
                vm.orgaoEntidadeTitular = undefined;
                vm.orgaoEntidadeAnterior = undefined;
                vm.ugProprietarioAnterior = undefined;
                vm.orgaoEntidadeProprietarioParcial = undefined;
                vm.idSenadoFederalAcessos = 2;
                vm.idTcuAcessos = 3;
                vm.idMinisterioDefesaAcessos = 17;
                vm.idSenadoFederalUgAcessos = 64;
                vm.idMinisterioPlanejamento = 20113;
                vm.codigoOrgaoEntidadeLocatario = undefined;
                vm.codigoUgLocatario = undefined;
                vm.cnpjUgEspecifica = undefined;
                vm.cnpjUgEspecificaLocatario = undefined;

            }],
            controllerAs: "ctrl",
            link: function ($scope, el, attrs, ctrl) {
                var vm = $scope,
                    init,
                    setarUgsByOrgao,
                    // Tipos proprietário parcial
                    ID_TERCEIRO_PROPRIETARIO_PARCIAL = 1,
                    ID_AUTARQUIAFUNDACAO_PARCIAL = 2,
                    ID_UNIAO_PROPRIETARIO_PARCIAL = 3,

                    // Tipos proprietário real
                    ID_UNIAO_PROPRIETARIO_REAL = 1,
                    ID_AUTARQUIAFUNDACAO_REAL = 5,
                    ID_TERCEIROS_PROPRIETARIO_REAL = 6,

                    // Formas de incorporação
                    ID_ORIGINALMENTE_DA_UNIAO = 1,
                    ID_POR_AQUISICAO = 2,
                    ID_POR_FRACIONAMENTO = 3,
                    ID_POR_FUSAO = 4,
                    ID_IMOVEL_DE_TERCEIROS = 5,

                    // Tipos de aquisicao
                    ID_TIPO_AQUISICAO_HERANCA_JACENTE = 6,
                    ID_TIPO_AQUISICAO_SUCESSAO_POR_ENTIDADES_APF = 9,
                    ID_TIPO_AQUISICAO_ARREMATACAO_JUDICIAL = 10,
                    ID_TIPO_AQUISICAO_REGISTRO_POR_APOSSAMENTO = 11,
                    ID_TIPO_AQUISICAO_DESAPROPRIACAO_JUDICIAL = 16,

                    // Tipos Imovel
                    ID_TIPO_IMOVEL_ESPELHO_DAGUA = 3,
                    ID_TIPO_IMOVEL_CAVIDADE = 5,

                    // Mapa UF -> código da SPU regional responsável
                    UG_SPU_POR_UF = {
                        'AC': '201035', 'AL': '170067', 'AM': '170211', 'AP': '201032',
                        'BA': '170083', 'CE': '170043', 'DF': '170021', 'ES': '170104',
                        'GO': '170199', 'MA': '170029', 'MG': '170098', 'MS': '170112',
                        'MT': '170193', 'PA': '170128', 'PB': '170053', 'PE': '170062',
                        'PI': '170036', 'PR': '170164', 'RJ': '170127', 'RN': '170048',
                        'RO': '201033', 'RR': '201034', 'RS': '170188', 'SC': '170173',
                        'SE': '170070', 'SP': '170151', 'TO': '201039', 'EX': '170021'
                    },

                    // Mapa código UG -> nome da UG (para fallback quando API falhar)
                    MAPA_UGAS = {
                        '201035': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/AC',
                        '170067': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/AL',
                        '170211': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/AM',
                        '201032': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/AP',
                        '170083': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/BA',
                        '170043': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/CE',
                        '170021': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/DF',
                        '170104': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/ES',
                        '170199': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/GO',
                        '170029': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/MA',
                        '170098': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/MG',
                        '170112': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/MS',
                        '170193': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/MT',
                        '170128': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/PA',
                        '170053': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/PB',
                        '170062': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/PE',
                        '170036': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/PI',
                        '170164': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/PR',
                        '170127': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/RJ',
                        '170048': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/RN',
                        '201033': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/RO',
                        '201034': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/RR',
                        '170188': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/RS',
                        '170173': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/SC',
                        '170070': 'SUPERINTENDENCIA DO PATRIMONIO DA UNIAO/SE',
                        '170151': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/SP',
                        '201039': 'SUPERINTENDÊNCIA DO PATRIMÔNIO DA UNIÃO/TO',
                        '170011': 'SECRETARIA DO PATRIMÔNIO DA UNIÃO'
                    };

                vm.exibirNovaEntidade = false;
                vm.novaEntidade = {};

                // UG SPU da UF do imóvel para preenchimento automático
                vm.ugSpuUf = {
                    id: null,
                    codigo: '',
                    nome: '',
                    cnpj: ''
                };

                // UG Autarquia para preenchimento automático
                vm.ugAutarquia = {
                    id: null,
                    codigo: '',
                    nome: '',
                    cnpj: ''
                };

                // UG do usuário logado para preenchimento automático
                vm.ugUsuarioLogadoCompleta = {
                    id: null,
                    codigo: '',
                    nome: '',
                    cnpj: '',
                    isGrpu: false
                };

                init = function () {

                    // Flag: false quando for edição de imóvel com UG já salva no banco.
                    // Impede que o auto-preenchimento sobrescreva o valor do banco na carga inicial.
                    // Será marcada como true pelos watchers quando o usuário fizer uma alteração intencional.
                    vm._ugInicializada = !(vm.imovel && vm.imovel.id && vm.imovel.proprietario && vm.imovel.proprietario.codigoUgResponsavel);

                    console.log('[dadosProprietario] init - codigoUgResponsavel do banco:', vm.imovel.proprietario.codigoUgResponsavel);
                    console.log('[dadosProprietario] init - _ugInicializada:', vm._ugInicializada);

                    if (vm.isLocacao() && vm.imovel.proprietario.codigoOrgaoEntidade != null && vm.imovel.proprietario.codigoOrgaoEntidade != undefined
                        && vm.imovel.proprietario.codigoOrgaoEntidade.toString().length < 14) {
                        //CNPJ do Locatário
                        vm.imovel.proprietario.codigoOrgaoEntidade = vm.imovel.proprietario.codigoOrgaoEntidade.toString().padStart(14, '0');
                    }

                    
                    if (vm.imovel.cnpjUgEspecificaLocatario != null && vm.imovel.cnpjUgEspecificaLocatario != undefined) {
                        vm.imovel.cnpjUgEspecificaLocatario = cpfCnpjMaskService.getMask(vm.imovel.cnpjUgEspecificaLocatario);                            
                    }

                    vm.tiposProprietarioReal = [];
                    vm.tiposProprietarioRealLocatario = [];
                    TipoProprietarioReal.query().$promise.then(function (result) {
                        vm.tiposProprietarioReal = result;

                        vm.tiposProprietarioRealLocatario = result.filter(function (tipo) {
                            return tipo.id === ID_UNIAO_PROPRIETARIO_REAL || tipo.id === ID_AUTARQUIAFUNDACAO_REAL;
                        });

                    });

                    if (!!vm.imovel.proprietarioAnterior && !!vm.imovel.proprietarioAnterior.entidadeExtinta) {
                        var entidadeExtintaFormat = {};
                        entidadeExtintaFormat.value = vm.imovel.proprietarioAnterior.entidadeExtinta.descricao.toLowerCase();
                        entidadeExtintaFormat.display = vm.imovel.proprietarioAnterior.entidadeExtinta.descricao;
                        entidadeExtintaFormat.id = vm.imovel.proprietarioAnterior.entidadeExtinta.id;
                        vm.imovel.proprietarioAnterior.entidadeExtinta = entidadeExtintaFormat;
                    }

                    if (vm.imovel.proprietario.tipoProprietarioReal != null && vm.imovel.proprietario.tipoProprietarioReal.id > 4 && vm.imovel.proprietario.orgaoEntidade) {
                        vm.orgaosEntidades = {};
                        vm.orgaosEntidades = Dominio.Mocks.orgaosEntidades();
                        vm.imovel.proprietario.autarquiaSelecionada = {};
                        vm.imovel.proprietario.autarquiaSelecionada = { nome: vm.imovel.proprietario.orgaoEntidade };
                    } else {
                        vm.orgaosEntidades = Dominio.Mocks.orgaosEntidades();
                    }

                    vm.tiposTitular = [];
                    vm.tiposProprietarioAnterior = [];
                    vm.ugsReal = [{ id: 64, nome: 'SENADO FEDERAL' }];
                    vm.ugsTitular = undefined;
                    vm.ugsAnterior = undefined;
                    vm.ugsMinisterio = Dominio.Mocks.ugsMinisterio();
                    vm.ugsSenado = Dominio.Mocks.ugsSenado();
                    vm.ugsTcu = Dominio.Mocks.ugsTcu();
                    vm.terceiroProprietario = {};
                    vm.terceiroProprietarioAnterior = {};
                    vm.tiposProprietarioReal = TipoProprietarioReal.query();
                    vm.tiposProprietarioParcial = {};
                    Dominio.tiposProprietarioParcial().then(function (result) {
                        vm.tiposProprietarioParcial = result;
                    });

                    TipoProprietarioReal.query().$promise.then(function (result) {
                        vm.tiposProprietarioAnterior = angular.copy(result);
                        vm.tiposTitular = result;

                    });

                    if (ctrl.orgaoEntidadeProprietario !== null && angular.isDefined(ctrl.orgaoEntidadeProprietario)) {
                        ctrl.orgaoEntidadeProprietarioParcial.id = ctrl.orgaoEntidadeProprietario.id = vm.imovel.proprietario.codigoOrgaoEntidade ? vm.imovel.proprietario.codigoOrgaoEntidade : undefined;
                        ctrl.orgaoEntidadeProprietarioParcial.nome = ctrl.orgaoEntidadeProprietario.nome = vm.imovel.proprietario.orgaoEntidade ? vm.imovel.proprietario.orgaoEntidade : undefined;
                    }

                    if (ctrl.ugProprietarioParcial && ctrl.ugProprietario) {
                        ctrl.ugProprietarioParcial.id = ctrl.ugProprietario.id = vm.imovel.proprietario.codigoUg ? vm.imovel.proprietario.codigoUg : undefined;
                        ctrl.ugProprietarioParcial.nome = ctrl.ugProprietario.nome = vm.imovel.proprietario.unidadeUsuario ? vm.imovel.proprietario.unidadeUsuario : undefined;
                    }

                    if (ctrl.orgaoEntidadeAnterior && vm.imovel.proprietarioAnterior) {
                        ctrl.orgaoEntidadeAnterior.id = vm.imovel.proprietarioAnterior.codigoOrgaoEntidade ? vm.imovel.proprietarioAnterior.codigoOrgaoEntidade : undefined;
                        ctrl.orgaoEntidadeAnterior.nome = vm.imovel.proprietarioAnterior.orgaoEntidade ? vm.imovel.proprietarioAnterior.orgaoEntidade : undefined;
                        ctrl.ugProprietarioAnterior.id = vm.imovel.proprietarioAnterior.codigoUg ? vm.imovel.proprietarioAnterior.codigoUg : undefined;
                        ctrl.ugProprietarioAnterior.nome = vm.imovel.proprietarioAnterior.unidadeUsuario ? vm.imovel.proprietarioAnterior.unidadeUsuario : undefined;
                    }

                    // ===== BUSCA UGs COMENTADA TEMPORARIAMENTE =====

                    buscarCpfCnpjService.buscarUgs().then(function (response) {
                      var lista = Array.isArray(response.data) ? response.data
                                : (response.data && Array.isArray(response.data.resposta) ? response.data.resposta : []);
                      vm.listaUgs = lista.map(function (ug) {
                        return {
                            codigo: String(ug.codigo), // ← FORÇA STRING
                            nome: ug.nome,
                            display: String(ug.codigo) + ' - ' + ug.nome
                        };
                      });

                      vm.verificarEAplicarUg();

                      // Se for edição com UG já salva no banco, popula apenas o display sem recalcular
                      if (!vm._ugInicializada && vm.imovel.proprietario.codigoUgResponsavel) {
                          vm.popularDisplayUgResponsavel(vm.imovel.proprietario.codigoUgResponsavel);
                      }

                      // Aplica UG Síndica após lista carregar (garante que listaUgs está disponível)
                      if (vm.imovel.proprietario.codigoUgSindica || vm.imovel.proprietario.codigoUgResponsavel) {
                        vm.aplicarUgSindicaInicial();
                      }
                    });

                    setarUgsByOrgao();

                    // Buscar dados da UG do usuário logado na inicialização (necessário para is_grpu)
                    if (ctrl.codigoUGUsuarioLogado) {
                        vm.buscarDadosUgUsuarioLogado(ctrl.codigoUGUsuarioLogado);
                    }

                    // Buscar SPU da UF do imóvel se já houver UF definida (edição)
                    var ufInicial = vm.imovel.enderecamento && vm.imovel.enderecamento.uf;
                    if (ufInicial) {
                        vm.buscarDadosUgSpuPorUf(ufInicial);
                    }

                    // Verificar se deve aplicar UG automaticamente após carregar dados
                    $timeout(function () {
                        vm.verificarEAplicarUg();
                    }, 100);

                    // Verificar se já existe UG da autarquia na inicialização
                    if (vm.imovel.proprietario.ugAutarquia) {
                        vm.buscarDadosUgAutarquia(vm.imovel.proprietario.ugAutarquia);
                    }
                };

                setarUgsByOrgao = function () {
                    if (ctrl.orgaoEntidadeTitular && ctrl.orgaoEntidadeTitular.id === ctrl.idSenadoFederalAcessos) {
                        vm.ugsTitular = vm.ugsSenado;
                    } else if (ctrl.orgaoEntidadeTitular && ctrl.orgaoEntidadeTitular.id === ctrl.idTcuAcessos) {
                        vm.ugsTitular = vm.ugsTcu;
                    } else if (ctrl.orgaoEntidadeTitular && ctrl.orgaoEntidadeTitular.id === ctrl.idMinisterioDefesaAcessos) {
                        vm.ugsTitular = vm.ugsMinisterio;
                    }

                    if (ctrl.orgaoEntidadeProprietarioParcial && ctrl.orgaoEntidadeProprietarioParcial.id === ctrl.idSenadoFederalAcessos) {
                        vm.ugs = vm.ugsSenado;
                    } else if (ctrl.orgaoEntidadeProprietarioParcial && ctrl.orgaoEntidadeProprietarioParcial.id === ctrl.idTcuAcessos) {
                        vm.ugs = vm.ugsTcu;
                    } else if (ctrl.orgaoEntidadeProprietarioParcial && ctrl.orgaoEntidadeProprietarioParcial.id === ctrl.idMinisterioDefesaAcessos) {
                        vm.ugs = vm.ugsMinisterio;
                    }

                    if (ctrl.orgaoEntidadeAnterior && ctrl.orgaoEntidadeAnterior.id === ctrl.idSenadoFederalAcessos) {
                        vm.ugsAnterior = vm.ugsSenado;
                    } else if (ctrl.orgaoEntidadeAnterior && ctrl.orgaoEntidadeAnterior.id === ctrl.idTcuAcessos) {
                        vm.ugsAnterior = vm.ugsTcu;
                    } else if (ctrl.orgaoEntidadeAnterior && ctrl.orgaoEntidadeAnterior.id === ctrl.idMinisterioDefesaAcessos) {
                        vm.ugsAnterior = vm.ugsMinisterio;
                    }
                };

                vm.isOriginalmenteDaUniao = function () {

                    if (vm.imovel.incorporacao.tipoIncorporacao) {
                        var isOriginalmenteDaUniao = vm.imovel.incorporacao.tipoIncorporacao.id === ID_ORIGINALMENTE_DA_UNIAO;
                        var proprietarioRealUniao = undefined;
                        if (isOriginalmenteDaUniao) {
                            for (var i = 0; i < vm.tiposProprietarioReal.length; i++) {
                                if (vm.tiposProprietarioReal[i].id === ID_UNIAO_PROPRIETARIO_REAL) {
                                    proprietarioRealUniao = vm.tiposProprietarioReal[i];
                                    break;
                                }
                            }
                            if (vm.imovel.proprietario) {
                                vm.imovel.proprietario.tipoProprietarioReal = proprietarioRealUniao;
                            } else {
                                console.log('vm.imovel.proprietario undefined');
                            }
                        }
                    }
                    return isOriginalmenteDaUniao;
                };

                vm.isAquisicaoPorSucessaoPorEntidadesdaAPF = function () {

                    var isAquisicaoSucessaoPorEntidadesAPF = vm.isAquisicao() &&
                        vm.imovel.incorporacao.tipoAquisicao &&
                        vm.imovel.incorporacao.tipoAquisicao.id === ID_TIPO_AQUISICAO_SUCESSAO_POR_ENTIDADES_APF;

                    if (isAquisicaoSucessaoPorEntidadesAPF && vm.imovel.proprietarioAnterior) {
                        vm.imovel.proprietarioAnterior.tipoProprietarioReal = undefined;
                    }
                    return isAquisicaoSucessaoPorEntidadesAPF;
                };



                vm.mostraOpcaoTerceiroNaoPossuiCpfOuCnpj = function () {
                    var idTipoAquisicao;
                    if (vm.imovel.incorporacao.tipoAquisicao) {
                        idTipoAquisicao = vm.imovel.incorporacao.tipoAquisicao.id;
                    }
                    return idTipoAquisicao === ID_TIPO_AQUISICAO_DESAPROPRIACAO_JUDICIAL ||
                        idTipoAquisicao === ID_TIPO_AQUISICAO_ARREMATACAO_JUDICIAL ||
                        idTipoAquisicao === ID_TIPO_AQUISICAO_HERANCA_JACENTE ||
                        idTipoAquisicao === ID_TIPO_AQUISICAO_REGISTRO_POR_APOSSAMENTO;

                };


                vm.isAquisicao = function () {
                    var isAquisicao = false;
                    if (vm.imovel.incorporacao.tipoIncorporacao && vm.imovel.incorporacao.tipoIncorporacao.id === ID_POR_AQUISICAO) {
                        isAquisicao = true;
                    }

                    return isAquisicao;
                };

                vm.isRecebimentoEmDoacao = function () {
                    var isRecebimentoEmDoacao;
                    if (vm.imovel.incorporacao.tipoAquisicao) {
                        isRecebimentoEmDoacao =
                            vm.imovel.incorporacao.tipoAquisicao.codigoSistemico === "INC_FORM_DADOS_GERAIS_RECEBIMENTO_DOAC";
                    }
                    return isRecebimentoEmDoacao;
                }

                vm.isLocacao = function () {
                    var isLocacao = false;
                    if (vm.imovel.incorporacao.tipoContratoPessoal &&
                        vm.imovel.incorporacao.tipoContratoPessoal.descricao === 'Locação') {
                        isLocacao = true;
                    }

                    return isLocacao;
                };

                vm.setUniaoCnpj = function () {
                    if (vm.imovel.proprietario.tipoProprietarioReal.descricao === TIPO_PROPRIETARIO_UNIAO) {
                        vm.imovel.cnpjProprietario = CNPJ_UNIAO;
                    } else {
                        vm.imovel.cnpjProprietario = null;
                    }
                };

                vm.setUniaoCnpjLocatario = function () {
                    if (vm.imovel.proprietario.tipoProprietarioLocatario.codigoSistemico === 'INC_FORM_PROPRIETARIO_UNIAO_PROP') {
                        vm.imovel.proprietario.cnpjLocatario = CNPJ_UNIAO;
                    } else {
                        vm.imovel.proprietario.cnpjLocatario = null;
                    }
                };

                vm.naoExibeTerceirosProprietarioReal = function (proprietario) {
                    return proprietario.id == ID_TERCEIROS_PROPRIETARIO_REAL &&
                        !vm.isAquisicaoPorSucessaoPorEntidadesdaAPF();
                };

                vm.naoExibeTerceirosTitular = function (proprietario) {
                    return proprietario.id == ID_TERCEIROS_PROPRIETARIO_REAL;

                };

                vm.naoExibeUniaoProprietarioAnterior = function (proprietario) {
                    return vm.imovel.proprietario.tipoProprietarioReal && vm.imovel.proprietario.tipoProprietarioReal.id === ID_UNIAO_PROPRIETARIO_REAL &&
                        proprietario.id == ID_UNIAO_PROPRIETARIO_REAL;

                };

                vm.isImovelDeTerceiros = function () {
                    var isImovelDeTerceiros;
                    if (vm.imovel.incorporacao.tipoIncorporacao) {
                        isImovelDeTerceiros = vm.imovel.incorporacao.tipoIncorporacao.id === ID_IMOVEL_DE_TERCEIROS;
                        var proprietarioRealUniao = undefined;
                        if (isImovelDeTerceiros) {
                            for (var i = 0; i < vm.tiposProprietarioReal.length; i++) {
                                if (vm.tiposProprietarioReal[i].id === ID_TERCEIROS_PROPRIETARIO_REAL) {
                                    proprietarioRealUniao = vm.tiposProprietarioReal[i];
                                    break;
                                }
                            }
                            vm.imovel.proprietario.tipoProprietarioReal = proprietarioRealUniao;
                        }
                    }
                    return isImovelDeTerceiros;

                };



                vm.isTipoImovelCavidadeOuEspelhoDagua = function () {

                    if (vm.imovel.tipoImovel) {
                        var isTipoImovelCavidadeOuEspelhoDagua = vm.imovel.tipoImovel.id === ID_TIPO_IMOVEL_ESPELHO_DAGUA ||
                            vm.imovel.tipoImovel.id === ID_TIPO_IMOVEL_CAVIDADE;
                        var proprietarioRealUniao = undefined;
                        if (isTipoImovelCavidadeOuEspelhoDagua) {
                            for (var i = 0; i < vm.tiposProprietarioReal.length; i++) {
                                if (vm.tiposProprietarioReal[i].id === ID_UNIAO_PROPRIETARIO_REAL) {
                                    proprietarioRealUniao = vm.tiposProprietarioReal[i];
                                    break;
                                }
                            }
                            vm.imovel.proprietario.tipoProprietarioReal = proprietarioRealUniao;
                            vm.imovel.proprietario.isIntegralmente = 'true';
                        }
                    }
                    return isTipoImovelCavidadeOuEspelhoDagua;
                };

                var proprietarioParcialRemovido = undefined;
                vm.isParcialmente = function () {
                    if (vm.imovel.proprietario) {
                        if (vm.imovel.proprietario.isIntegralmente == undefined ||
                            vm.imovel.proprietario.isIntegralmente == null) {
                            vm.imovel.proprietario.isIntegralmente = 'true';
                        }

                        var idTipoProprietarioSelecionado;
                        if (proprietarioParcialRemovido && !vm.tiposProprietarioParcial.includes(proprietarioParcialRemovido)) {
                            vm.tiposProprietarioParcial.push(proprietarioParcialRemovido);
                        }
                        var isParcialmente = vm.imovel.proprietario.isIntegralmente != 'true';
                        if (vm.imovel.proprietario && vm.imovel.proprietario.tipoProprietarioReal) {
                            idTipoProprietarioSelecionado = vm.imovel.proprietario.tipoProprietarioReal.id;
                        }

                        var remover = undefined;
                        if (idTipoProprietarioSelecionado === ID_TERCEIROS_PROPRIETARIO_REAL) {
                            remover = ID_TERCEIRO_PROPRIETARIO_PARCIAL;
                        } else if (idTipoProprietarioSelecionado === ID_UNIAO_PROPRIETARIO_REAL) {
                            remover = ID_UNIAO_PROPRIETARIO_PARCIAL;
                        } else if (idTipoProprietarioSelecionado === ID_AUTARQUIAFUNDACAO_REAL) {
                            remover = ID_AUTARQUIAFUNDACAO_PARCIAL;
                        }
                        if (isParcialmente) {
                            for (var j = 0; j < vm.tiposProprietarioParcial.length; j++) {
                                if (vm.tiposProprietarioParcial[j].id === remover) {
                                    proprietarioParcialRemovido = vm.tiposProprietarioParcial.splice(j, 1)[0];
                                }
                            }
                        }

                        return isParcialmente;
                    } else {
                        console.log('vm.imovel.proprietario');
                    }
                };


                vm.isTipoProprietarioAutarquiaFundacao = function () {

                    var isTipoProprietarioAutarquiaFundacao;
                    if (vm.imovel.proprietario && vm.imovel.proprietario.tipoProprietarioReal) {
                        isTipoProprietarioAutarquiaFundacao = (vm.imovel.proprietario.tipoProprietarioReal.id === ID_AUTARQUIAFUNDACAO_REAL);
                    }

                    return isTipoProprietarioAutarquiaFundacao;
                };

                vm.buscaUgsProprietarioParcial = function () {

                    vm.imovel.proprietario.codigoOrgaoEntidade = ctrl.orgaoEntidadeProprietarioParcial.id;
                    if (ctrl.orgaoEntidadeProprietarioParcial.id === ctrl.idSenadoFederalAcessos) {
                        vm.ugs = vm.ugsSenado;
                    } else if (ctrl.orgaoEntidadeProprietarioParcial.id === ctrl.idTcuAcessos) {
                        vm.ugs = vm.ugsTcu;
                    } else if (ctrl.orgaoEntidadeProprietarioParcial.id === ctrl.idMinisterioDefesaAcessos) {
                        vm.ugs = vm.ugsMinisterio;

                    }
                };

                vm.setDadosUgAnterior = function () {
                    if (vm.imovel.proprietarioAnterior) {
                        vm.imovel.proprietarioAnterior.codigoUg = ctrl.ugProprietarioAnterior.id;
                        vm.imovel.proprietarioAnterior.unidadeUsuario = ctrl.ugProprietarioAnterior.nome;
                    }
                };

                vm.setDadosUgParcial = function () {
                    vm.imovel.proprietario.codigoUg = ctrl.ugProprietarioParcial.id;
                    vm.imovel.proprietario.unidadeUsuario = ctrl.ugProprietarioParcial.nome;
                };

                vm.buscaUgsProprietarioAnterior = function () {
                    vm.imovel.proprietarioAnterior.codigoOrgaoEntidade = ctrl.orgaoEntidadeAnterior.id;
                    vm.imovel.proprietarioAnterior.orgaoEntidade = ctrl.orgaoEntidadeAnterior.nome;
                    if (ctrl.orgaoEntidadeAnterior.id === ctrl.idSenadoFederalAcessos) {
                        vm.ugsAnterior = vm.ugsSenado;
                    } else if (ctrl.orgaoEntidadeAnterior.id === ctrl.idTcuAcessos) {
                        vm.ugsAnterior = vm.ugsTcu;
                    } else if (ctrl.orgaoEntidadeAnterior.id === ctrl.idMinisterioDefesaAcessos) {
                        vm.ugsAnterior = vm.ugsMinisterio;
                    }
                };

                vm.verificaUgProprietarioReal = function () {

                    if (ctrl.codigoUGUsuarioLogado && ctrl.ugProprietarioAnterior) {
                        var codigoUGUsuarioLogado = ctrl.codigoUGUsuarioLogado,
                            idUgProprietarioAnterior = ctrl.ugProprietarioAnterior.id;

                        if (codigoUGUsuarioLogado === idUgProprietarioAnterior) {
                            MensagensService.exibirMensagemErro("O proprietário anterior não pode ser o mesmo proprietário do imóvel informado");
                        } else {
                            vm.setDadosUgAnterior();
                        }
                    }
                };

                vm.limparProprietarioAnterior = function () {
                    ctrl.ugProprietarioAnterior = undefined;
                    ctrl.orgaoEntidadeAnterior = undefined;
                    vm.imovel.proprietarioAnterior = undefined;
                };


                vm.isTipoProprietarioTerceiros = function () {
                    if (vm.imovel.proprietario && vm.imovel.proprietario.tipoProprietarioReal) {
                        return vm.imovel.proprietario.tipoProprietarioReal.id === ID_TERCEIROS_PROPRIETARIO_REAL;
                    }
                };

                vm.isTipoProprietarioParcialAutarquiaFundacao = function () {

                    if (vm.imovel.proprietario && vm.imovel.proprietario.tipoProprietarioParcial) {
                        return vm.imovel.proprietario.tipoProprietarioParcial.id === ID_AUTARQUIAFUNDACAO_PARCIAL &&
                            vm.imovel.proprietario.isIntegralmente != 'true';
                    }

                };


                vm.isTipoProprietarioAnteriorAutarquiaFundacao = function () {
                    if (vm.imovel.proprietarioAnterior && vm.imovel.proprietarioAnterior.tipoProprietarioReal) {
                        return vm.imovel.proprietarioAnterior.tipoProprietarioReal.id === ID_AUTARQUIAFUNDACAO_REAL;
                    }
                };

                vm.isTipoProprietarioAnteriorTerceiros = function () {
                    if (vm.imovel.proprietarioAnterior && vm.imovel.proprietarioAnterior.tipoProprietarioReal) {
                        return vm.imovel.proprietarioAnterior.tipoProprietarioReal.id === ID_TERCEIROS_PROPRIETARIO_REAL;
                    }

                };


                vm.isFracionamento = function () {
                    if (vm.imovel.incorporacao.tipoIncorporacao) {
                        return (vm.imovel.incorporacao.tipoIncorporacao.id === ID_POR_FRACIONAMENTO);
                    }


                };

                vm.isFusao = function () {
                    if (vm.imovel.incorporacao.tipoIncorporacao) {
                        return (vm.imovel.incorporacao.tipoIncorporacao.id === ID_POR_FUSAO);
                    }

                };

                NotificadorService.subscribe($scope, function (evento, escopo, objeto) {
                    ctrl.ugProprietario = objeto.ugProprietario;
                    ctrl.ugProprietarioParcial = objeto.ugParcial;
                    ctrl.ugProprietarioTitular = objeto.ugTitular;
                    ctrl.orgaoEntidadeProprietario = objeto.orgaoProprietario;
                    ctrl.orgaoEntidadeTitular = objeto.orgaoTitular;
                    ctrl.orgaoEntidadeAnterior = objeto.orgaoAnterior;
                    ctrl.ugProprietarioAnterior = objeto.ugAnterior;
                    ctrl.orgaoEntidadeProprietarioParcial = objeto.orgaoParcial;
                    setarUgsByOrgao();
                }, NOTIFICADORES.SETAR_UGS_ORGAOS);

                NotificadorService.subscribe($scope, function (evento, escopo, objeto) {
                    ctrl.ugProprietario = objeto.ugProprietario;
                    ctrl.orgaoEntidadeProprietario = objeto.orgaoProprietario;
                    setarUgsByOrgao();
                }, NOTIFICADORES.SETAR_PROPRIETARIO_FUSAO);

                NotificadorService.subscribe($scope, function (evento, escopo, objeto) {
                    ctrl.orgaoEntidadeProprietarioParcial = undefined;
                    ctrl.ugProprietarioParcial = undefined;
                    ctrl.orgaoEntidadeTitular = undefined;
                    ctrl.ugProprietarioTitular = undefined;
                    ctrl.orgaoEntidadeProprietario = undefined;
                    ctrl.ugProprietario = undefined;
                    ctrl.ugProprietarioAnterior = undefined;
                    ctrl.orgaoEntidadeAnterior = undefined;
                }, NOTIFICADORES.LIMPAR_UGS_ORGAOS);

                // Watchers para monitorar mudanças nos campos relevantes.
                // Observam o ID (primitivo) em vez do objeto inteiro para evitar falsos disparos
                // causados por funções como isOriginalmenteDaUniao() que substituem a referência
                // do objeto durante o digest cycle sem que o usuário tenha feito nada.
                // Nota: NÃO exigimos oldVal !== undefined aqui — o novo cadastro precisa que o
                // watcher dispare mesmo quando oldVal é undefined (primeira seleção do usuário).
                // A proteção contra sobrescrita em edição é feita pela flag _ugInicializada
                // dentro de aplicarUgAutomatica().
                $scope.$watch('imovel.incorporacao.tipoIncorporacao.id', function (newVal, oldVal) {
                    if (newVal !== undefined && newVal !== oldVal) {
                        vm._ugInicializada = true;
                        vm.verificarEAplicarUg();
                    }
                });

                $scope.$watch('imovel.proprietario.tipoProprietarioReal.id', function (newVal, oldVal) {
                    if (newVal !== undefined && newVal !== oldVal) {
                        vm._ugInicializada = true;
                        vm.verificarEAplicarUg();
                    }
                });

                // Watcher para monitorar mudanças na UG da autarquia
                $scope.$watch('imovel.proprietario.ugAutarquia', function (newVal, oldVal) {
                    if (newVal && newVal !== oldVal) {
                        vm._ugInicializada = true;
                        vm.buscarDadosUgAutarquia(newVal);
                    }
                });

                // Watcher para monitorar mudança de UF do imóvel — invalida cache da SPU-[UF]
                $scope.$watch('imovel.enderecamento.uf', function (newVal, oldVal) {
                    if (newVal && newVal !== oldVal) {
                        vm._ugInicializada = true;
                        vm.ugSpuUf = { id: null, codigo: '', nome: '', cnpj: '' };
                        vm.verificarEAplicarUg();
                    }
                });

                // Watcher para monitorar mudança do Órgão Locatário (Locação)
                $scope.$watch('imovel.proprietario.tipoProprietarioLocatario.id', function (newVal, oldVal) {
                    if (newVal !== undefined && newVal !== oldVal) {
                        vm._ugInicializada = true;
                        vm.verificarEAplicarUg();
                    }
                });

                // Watcher para monitorar preenchimento da UG do Locatário Autarquia/Fundação
                $scope.$watch('imovel.proprietario.ugLocatario', function (newVal, oldVal) {
                    if (newVal && newVal !== oldVal) {
                        vm._ugInicializada = true;
                        vm.verificarEAplicarUg();
                    }
                });


                vm.simulateQuery = false;
                vm.isDisabled = false;
                vm.querySearch = vm.querySearch;

                ExtintasService.query().$promise.then(function (result) {
                    vm.extintas = result;
                    vm.extintas2 = vm.loadAll();
                });

                vm.querySearch = function (query) {
                    var results = query ? vm.extintas2.filter(vm.createFilterFor(query)) : vm.extintas2,
                        deferred;
                    if (vm.simulateQuery) {
                        deferred = $q.defer();
                        $timeout(function () { deferred.resolve(results); }, Math.random() * 1000, false);
                        return deferred.promise;
                    } else {
                        return results;
                    }
                };

                vm.loadAll = function () {
                    return vm.extintas.map(function (extinta) {
                        return {
                            value: extinta.descricao.toLowerCase(),
                            display: extinta.descricao,
                            id: extinta.id
                        };
                    });
                };

                vm.createFilterFor = function (query) {
                    var lowercaseQuery = angular.lowercase(query);

                    return function filterFn(extinta) {
                        return (extinta.value.indexOf(lowercaseQuery) >= 0);
                    };
                }

                vm.getCodigoOrgaoEntidadeLocatario = function () {
                    vm.imovel.proprietario.codigoOrgaoEntidadeLocatario = vm.codigoOrgaoEntidadeLocatario ? vm.codigoOrgaoEntidadeLocatario.id : undefined;
                }

                vm.getCodigoUgLocatario = function () {
                    vm.imovel.proprietario.codigoUgLocatario = vm.codigoUgLocatario ? vm.codigoUgLocatario.id : undefined;
                }

                vm.mascaraCnpj = function (cnpj, name) {
                    if (cnpj != null && name != null) {
                        cnpj = cpfCnpjMaskService.getMask(cnpj);
                        document.getElementsByName(name)[0].value = cnpj;                        
                    }
                };

                vm.exibirFormularioEntidade = function () {
                    vm.exibirNovaEntidade = true;
                }

                vm.incluirEntidade = function () {

                    /*var results = texto ? vm.extintas2.filter(vm.createFilterFor(texto)) : vm.extintas2;
                    if (results == null || results.length == 0){
                      var extintaNova = {display: texto, value: texto.toLowerCase(), id:null, descricao: texto, ordem:null}
                      vm.extintas2.push(extintaNova);
                      vm.imovel.proprietarioAnterior.entidadeExtinta = extintaNova;
                    }*/

                    if (vm.novaEntidade.descricao) {
                        var results = vm.extintas2.filter(vm.createFilterFor(vm.novaEntidade.descricao));
                        if (results == null || results.length == 0) {
                            var extintaNova = {
                                display: vm.novaEntidade.descricao.toUpperCase(),
                                value: vm.novaEntidade.descricao.toUpperCase(),
                                id: null,
                                descricao: vm.novaEntidade.descricao.toUpperCase(),
                                sigla: vm.novaEntidade.sigla ? vm.novaEntidade.sigla.toUpperCase() : '',
                                ordem: vm.novaEntidade.ordem,
                                fundamento: vm.novaEntidade.fundamento
                            }
                            vm.extintas2.push(extintaNova);
                            vm.imovel.proprietarioAnterior.entidadeExtinta = extintaNova;
                        }
                    }

                    vm.exibirNovaEntidade = false;

                };

                // Função para buscar dados da SPU da UF do imóvel
                vm.buscarDadosUgSpuPorUf = function (uf) {
                    var codigoUg = UG_SPU_POR_UF[uf];
                    if (!codigoUg) {
                        return;
                    }
                    buscarCpfCnpjService.buscarAutarquia(codigoUg).then(function (response) {
                        var dados = Array.isArray(response.data) ? response.data[0]
                                  : (response.data && response.data.resposta ? response.data.resposta[0] : null);
                        var nomeUg = (dados && dados.nome) ? dados.nome
                                   : (vm.listaUgs ? (vm.listaUgs.find(function(u){ return u.codigo == codigoUg; }) || {}).nome || '' : '')
                                   || MAPA_UGAS[codigoUg] || '';
                        vm.ugSpuUf = {
                            id: (dados && dados.id) || null,
                            codigo: String(codigoUg),
                            nome: nomeUg,
                            cnpj: (dados && dados.cnpj) || ''
                        };
                        vm.aplicarUgAutomatica();
                    }).catch(function (error) {
                        console.error('[UG SPU-UF] Erro ao buscar UG SPU para UF ' + uf + ':', error);
                        var nomeFromList = vm.listaUgs ? (vm.listaUgs.find(function(u){ return u.codigo == codigoUg; }) || {}).nome || '' : '';
                        var nomeFromMapa = MAPA_UGAS[codigoUg] || '';
                        vm.ugSpuUf = { id: null, codigo: String(codigoUg), nome: nomeFromList || nomeFromMapa, cnpj: '' };
                        vm.aplicarUgAutomatica();
                    });
                };

                // Função para buscar dados da UG Autarquia
                vm.buscarDadosUgAutarquia = function (ugCodigo) {
                    if (!ugCodigo) {
                        return;
                    }

                    buscarCpfCnpjService.buscarAutarquia(ugCodigo).then(function (response) {
                        var dados = Array.isArray(response.data) ? response.data[0]
                                  : (response.data && response.data.resposta ? response.data.resposta[0] : null);
                        var nomeUg = (dados && dados.nome) ? dados.nome
                                   : (vm.listaUgs ? (vm.listaUgs.find(function(u){ return u.codigo == ugCodigo; }) || {}).nome || '' : '')
                                   || MAPA_UGAS[ugCodigo] || '';
                        vm.ugAutarquia = {
                            id: (dados && dados.id) || null,
                            codigo: (dados && dados.codigo) || ugCodigo,
                            nome: nomeUg,
                            cnpj: (dados && dados.cnpj) || ''
                        };
                        vm.aplicarUgAutomatica();
                    }).catch(function (error) {
                        console.error('[UG AUTARQUIA] Erro ao buscar UG Autarquia:', error);
                        var nomeFromList = vm.listaUgs ? (vm.listaUgs.find(function(u){ return u.codigo == ugCodigo; }) || {}).nome || '' : '';
                        var nomeFromMapa = MAPA_UGAS[ugCodigo] || '';
                        vm.ugAutarquia = { id: null, codigo: ugCodigo, nome: nomeFromList || nomeFromMapa, cnpj: '' };
                        vm.aplicarUgAutomatica();
                    });
                };

                // Função para buscar dados da UG do usuário logado
                vm.buscarDadosUgUsuarioLogado = function (ugCodigo) {
                    if (!ugCodigo) {
                        return;
                    }

                    buscarCpfCnpjService.buscarAutarquia(ugCodigo).then(function (response) {
                        var dados = Array.isArray(response.data) ? response.data[0]
                                  : (response.data && response.data.resposta ? response.data.resposta[0] : null);
                        if (dados && dados.nome) {
                            vm.ugUsuarioLogadoCompleta = {
                                id: dados.id || null,
                                codigo: dados.codigo || ugCodigo,
                                nome: dados.nome || ctrl.ugUsuarioLogado,
                                cnpj: dados.cnpj,
                                isGrpu: dados.isGrpu || false
                            };
                        } else {
                            vm.ugUsuarioLogadoCompleta = {
                                id: null,
                                codigo: ugCodigo,
                                nome: ctrl.ugUsuarioLogado || '',
                                cnpj: '',
                                isGrpu: false
                            };
                        }
                        vm.aplicarUgAutomatica();
                    }).catch(function (error) {
                        console.error('[UG USUARIO] Erro ao buscar UG do usuário:', error);
                        vm.ugUsuarioLogadoCompleta = {
                            id: null,
                            codigo: ugCodigo,
                            nome: ctrl.ugUsuarioLogado || '',
                            cnpj: '',
                            isGrpu: false
                        };
                        vm.aplicarUgAutomatica();
                    });
                };

                // Retorna true se o usuário logado pertence à SPU (central ou regional)
                vm.usuarioEhDaUniao = function () {
                    return vm.ugUsuarioLogadoCompleta.isGrpu === true
                        || vm.ugUsuarioLogadoCompleta.codigo == '170011'
                        || vm.ugUsuarioLogadoCompleta.codigo == '170021';
                };

                // Retorna true quando Contrato Pessoal = Locação E Órgão Locatário = Autarquia/Fundação
                vm.isLocacaoComLocatarioAutarquia = function () {
                    return vm.isLocacao() &&
                        vm.imovel.proprietario.tipoProprietarioLocatario &&
                        vm.imovel.proprietario.tipoProprietarioLocatario.codigoSistemico === 'INC_FORM_PROPRIETARIO_AUTARQ_PROP';
                };

                // Função para verificar condições e aplicar UG automaticamente
                vm.aplicarUgAutomatica = function () {
                    if (!vm.devePreencherUgAutomaticamente()) {
                        return;
                    }

                    // Preserva o valor do banco em modo edição/detalhe durante a carga inicial.
                    // O usuário deve alterar um campo relevante para acionar o recálculo.
                    if (!vm._ugInicializada) {
                        return;
                    }

                    var codigoUg, ugCompleta;
                    var tipoProprietario = vm.imovel.proprietario.tipoProprietarioReal.id;

                    // Autarquia/Fundação → UG da autarquia informada
                    if (tipoProprietario === ID_AUTARQUIAFUNDACAO_REAL && vm.ugAutarquia.codigo) {
                        codigoUg = vm.ugAutarquia.codigo;
                        ugCompleta = codigoUg + ' - ' + vm.ugAutarquia.nome;

                    // Terceiros → regras por prioridade:
                    //   1. Locação + Órgão Locatário = Autarquia/Fundação → UG do Locatário
                    //   2. Usuário da União → SPU-[UF]
                    //   3. Demais → UG do usuário logado
                    } else if (tipoProprietario === ID_TERCEIROS_PROPRIETARIO_REAL) {
                        if (vm.isLocacaoComLocatarioAutarquia() && vm.imovel.proprietario.ugLocatario) {
                            codigoUg = String(vm.imovel.proprietario.ugLocatario);
                            ugCompleta = codigoUg + ' - ' + (vm.imovel.proprietario.nomeLocatario || '');
                        } else if (vm.usuarioEhDaUniao() && vm.ugSpuUf.codigo) {
                            codigoUg = vm.ugSpuUf.codigo;
                            ugCompleta = codigoUg + ' - ' + vm.ugSpuUf.nome;
                        } else if (!vm.usuarioEhDaUniao() && vm.ugUsuarioLogadoCompleta.codigo) {
                            codigoUg = vm.ugUsuarioLogadoCompleta.codigo;
                            ugCompleta = codigoUg + ' - ' + vm.ugUsuarioLogadoCompleta.nome;
                        }

                    // União → SPU da UF do imóvel
                    } else if (tipoProprietario === ID_UNIAO_PROPRIETARIO_REAL && vm.ugSpuUf.codigo) {
                        codigoUg = vm.ugSpuUf.codigo;
                        ugCompleta = codigoUg + ' - ' + vm.ugSpuUf.nome;
                    }

                    if (ugCompleta) {
                        vm.imovel.proprietario.ugresponsavel = ugCompleta;
                        vm.imovel.proprietario.codigoUgResponsavel = codigoUg;

                        // Sempre sincroniza UG Síndica com a nova UG Responsável
                        //vm.imovel.proprietario.codigoUgSindica = String(codigoUg);
                        if (!vm.imovel.proprietario.codigoUgSindica){
                            vm.resolverObjetoUgSindica(codigoUg);
                        }
                    }
                };

                // Função para verificar se deve preencher UG automaticamente
                vm.devePreencherUgAutomaticamente = function () {
                    if (!vm.imovel.proprietario || !vm.imovel.proprietario.tipoProprietarioReal) {
                        return false;
                    }
                    var id = vm.imovel.proprietario.tipoProprietarioReal.id;
                    return id === ID_UNIAO_PROPRIETARIO_REAL
                        || id === ID_AUTARQUIAFUNDACAO_REAL
                        || id === ID_TERCEIROS_PROPRIETARIO_REAL;
                };

                // Função para verificar e aplicar UG quando necessário
                vm.verificarEAplicarUg = function () {
                    if (!vm.devePreencherUgAutomaticamente()) {
                        return;
                    }

                    var tipoProprietario = vm.imovel.proprietario.tipoProprietarioReal.id;
                    var uf = vm.imovel.enderecamento && vm.imovel.enderecamento.uf;

                    if (tipoProprietario === ID_AUTARQUIAFUNDACAO_REAL) {
                        var ugCodigo = vm.imovel.proprietario.ugAutarquia;
                        if (ugCodigo && !vm.ugAutarquia.nome) {
                            vm.buscarDadosUgAutarquia(ugCodigo);
                        } else if (vm.ugAutarquia.codigo) {
                            vm.aplicarUgAutomatica();
                        }

                    } else if (tipoProprietario === ID_UNIAO_PROPRIETARIO_REAL) {
                        // União → SPU da UF do imóvel
                        if (uf && UG_SPU_POR_UF[uf] !== vm.ugSpuUf.codigo) {
                            vm.buscarDadosUgSpuPorUf(uf);
                        } else if (vm.ugSpuUf.codigo) {
                            vm.aplicarUgAutomatica();
                        }

                    } else if (tipoProprietario === ID_TERCEIROS_PROPRIETARIO_REAL) {
                        if (vm.isLocacaoComLocatarioAutarquia()) {
                            // Locação + Locatário Autarquia/Fundação: usa UG do locatário se já disponível
                            if (vm.imovel.proprietario.ugLocatario) {
                                vm.aplicarUgAutomatica();
                            }
                        } else if (vm.usuarioEhDaUniao()) {
                            // Usuário da União: usa SPU-[UF]
                            if (uf && UG_SPU_POR_UF[uf] !== vm.ugSpuUf.codigo) {
                                vm.buscarDadosUgSpuPorUf(uf);
                            } else if (vm.ugSpuUf.codigo) {
                                vm.aplicarUgAutomatica();
                            }
                        } else {
                            // Demais usuários: usa UG do próprio usuário
                            if (vm.ugUsuarioLogadoCompleta.codigo) {
                                vm.aplicarUgAutomatica();
                            }
                        }
                    }
                };

                // Função chamada quando a UG da autarquia é atualizada (para uso externo)
                vm.onUgAutarquiaUpdated = function (ugCodigo) {
                    if (ugCodigo) {
                        vm.buscarDadosUgAutarquia(ugCodigo);
                    }
                };

                // Popula apenas o campo de display da UG Responsável a partir do código armazenado no banco.
                // Usada em modo edição/detalhe para exibir o valor sem recalcular.
                vm.popularDisplayUgResponsavel = function (codigo) {
                    if (!codigo) {
                        return;
                    }
                    var codigoStr = String(codigo).trim();
                    var ug = vm.listaUgs
                        ? vm.listaUgs.find(function (u) { return String(u.codigo).trim() === codigoStr; })
                        : null;
                    var nome = (ug && ug.nome) || MAPA_UGAS[codigoStr] || '';
                    vm.imovel.proprietario.ugresponsavel = nome ? (codigoStr + ' - ' + nome) : codigoStr;
                };

                // ===== FUNÇÕES UG SÍNDICA =====

                // Resolve o objeto UG Síndica a partir do código e seta no modelo
                vm.resolverObjetoUgSindica = function (codigo) {
                    if (!codigo || !vm.listaUgs || !vm.listaUgs.length) {
                        return;
                    }

                    var codigoStr = String(codigo).trim();
                    var encontrado = null;
                    for (var i = 0; i < vm.listaUgs.length; i++) {
                        if (String(vm.listaUgs[i].codigo).trim() === codigoStr) {
                            encontrado = vm.listaUgs[i];
                            break;
                        }
                    }

                    if (encontrado) {
                        vm.imovel.proprietario.ugSindicaObj = encontrado;
                        vm.ugSindicaDisplay = encontrado.display;
                    }
                };

                // Aplica UG Síndica inicial (edição ou novo cadastro)
                vm.aplicarUgSindicaInicial = function () {
                    // Cenário 1: Edição - já existe UG Síndica no banco
                    if (vm.imovel.proprietario.codigoUgSindica) {
                        vm.imovel.proprietario.codigoUgSindica = String(vm.imovel.proprietario.codigoUgSindica);
                        vm.resolverObjetoUgSindica(vm.imovel.proprietario.codigoUgSindica);
                        return;
                    }

                    // Cenário 2: Novo cadastro - copia da UG Responsável se disponível
                    if (vm.imovel.proprietario.codigoUgResponsavel) {
                        vm.imovel.proprietario.codigoUgSindica = String(vm.imovel.proprietario.codigoUgResponsavel);
                        vm.resolverObjetoUgSindica(vm.imovel.proprietario.codigoUgSindica);
                    }
                };

                // Handler para mudança manual do autocomplete
                vm.onUgSindicaChange = function (ug) {
                    if (!ug || !ug.codigo) {
                        return;
                    }
                    vm.imovel.proprietario.codigoUgSindica = String(ug.codigo);
                    vm.ugSindicaDisplay = ug.display;
                };

                init();
            }
        };
    }
})();

(function () {
    "use strict";
    directive.$inject = ["$rootScope", "TipoImovelFactory", "Dominio", "CidadesExtService", "ectCepService", "UfsService", "PaisesService", "MunicipioService", "NotificadorService", "MAPA_CONSTANTS_INCORPORACAO", "LogradouroService", "tabsIncorporacao", "CartografiaServiceInc", "MensagensService", "$mdDialog", "ABA_LOCALIZACAO"];
    angular.module("su-cadastro-imovel").directive("incLocalizacaoImovel", directive);

    function directive($rootScope, TipoImovelFactory, Dominio, CidadesExtService, ectCepService, UfsService,PaisesService, MunicipioService, NotificadorService, MAPA_CONSTANTS_INCORPORACAO, LogradouroService, tabsIncorporacao, CartografiaServiceInc, MensagensService, $mdDialog, ABA_LOCALIZACAO) {
        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/localizacao/templates/inc-localizacao-imovel.html",
            scope: {
                imovel: "=",
                action: "=",
                edificacoesUnidadeAutonoma: "=?",
                desabilitar: "="
            },
            link: function ($scope) {

                var vm = $scope,
                    notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES;

                var vmr = $rootScope;
                var self = this;

                vm.usuario = $rootScope.usuarioLogado || { permissoes: [], jurisdicoes: [] };
                vm.tiposImoveis = [];
                vm.paises = [];
                vm.cidadesExteriores = [];
                vm.tiposLogradouro = [];
                vm.municipios = [];
                vm.isCepGenerico = false;
                vm.definirMunicipio = false;
                vm.cidadeExterior = null;
                vm.imovelOriginal = {};
                vm.habilitaOpcaoGleba = true;
                vm.habilitaDemaisOpcoes = true;
                vm.enderecoDisabled = true;
                vm.jaConfirmou = false;
                //vm.desabilitarEdicaoUtilizacao = $scope.podeEditar;

                //  NotificadorService.subscribe($scope, function(event, scope, data) {
                //    vm.imovel.enderecamento.uf = data.uf;
                //    vm.imovel.enderecamento.municipio = data.municipio;
                //  }, notifiers.ATUALIZAR_UF);

                NotificadorService.subscribe($scope, function(evento, escopo, tabIndex) {
                  if (tabIndex == ABA_LOCALIZACAO){
                    vm.enderecoDisabled = true;
                    vm.imovel.uf='';
                  }
                }, notifiers.LIMPAR_ABA);

                $scope.$watch("usuarioLogado",

                    function(){
                        if($rootScope.usuarioLogado){
                            vm.usuario = $rootScope.usuarioLogado;
                            vm.getProprietariosCadastro();
                        }
                    }
                );

                function init() {
                    TipoImovelFactory.buscarTodos().then(function (result) {
                        vm.tiposImoveis = result.data;
                    });
                    LogradouroService.query().$promise.then(function (result) {
                        vm.tiposLogradouro = result;
                    });

                    vm.ufs = UfsService.UFS;
                    vm.paises = PaisesService.query()

                    if (isEdicao()) {
                        vm.imovelOriginal = angular.copy(vm.imovel);
                        vm.buscaEnderecoPorCepInit();
                    }
                    vm.handleTipoImovel();
                }

                vm.isTipoUnidadeAutonoma = function (tipoImovel) {
                    return tipoImovel.tipoPai === "UNIDADE AUTÔNOMA";
                };

                vm.isNotTipoUnidadeAutonomaPrimeiraParte = function (tipoImovel) {
                    return !vm.isTipoUnidadeAutonoma(tipoImovel) && tipoImovel.ordem <= 2;
                };

                vm.isNotTipoUnidadeAutonomaSegundaParte = function (tipoImovel) {
                    return !vm.isTipoUnidadeAutonoma(tipoImovel) && tipoImovel.ordem >= 6;
                };

                vm.getProprietariosCadastro = function() {
                    return vm.usuario.organizacao + (possuiPermissaoCadastroUniao() ? ' e UNIÃO' : '');
                };

                vm.buscaCidadesPorPais = function () {
                    if (vm.imovel.enderecamento.pais && !vm.isPaisBrasil()) {
                        vm.imovel.enderecamento.uf = 'EX';
                        vm.cidadesExteriores = CidadesExtService.query({id_pais: vm.imovel.enderecamento.pais.id});
                    }
                };

                vm.setMunicipio = function (nomeMunicipio, municipio) {
                  if (nomeMunicipio) {
                    vm.imovel.enderecamento.municipio = nomeMunicipio;
                    //NotificadorService.notify(vm, { municipio: nomeMunicipio, uf:  vm.imovel.enderecamento.uf}, notifiers.ENDERECAMENTO_UF_MUNICIPIO);
                  }

                  if (municipio){
                    //essencial na validação do shapeFile
                    vm.imovel.enderecamento.codMunicipio = municipio.codigo;
                  }
                };

                vm.isPaisBrasil = function() {
                    return vm.imovel.enderecamento.pais.id === 1;
                };

                vm.isImovelEspelhoDagua = function() {
                    return angular.isDefined(vm.imovel.tipoImovel) && vm.imovel.tipoImovel.id === 3;
                };

                vm.isCavidadeNaturalSubterranea = function() {
                  return angular.isDefined(vm.imovel.tipoImovel) && vm.imovel.tipoImovel.id === 5;
                };

                vm.isImovelEspelhoDaguaOuCavidade = function() {
                  return angular.isDefined(vm.imovel.tipoImovel)  &&
                  (vm.imovel.tipoImovel.id === 5 || vm.imovel.tipoImovel.id === 3);
                };

                vm.buscaEnderecoPorCepInit = function() {
                   var cep = vm.imovel.enderecamento.cep;
                    //if (cep && cep.length === 8 && !vm.isImovelEspelhoDagua()) {
                    if (cep && cep.length === 8 ) { //Precisa recuperar o codMunicipio caso altere shapeFile
                        ectCepService.get({cep: cep}).$promise.then(function(data){
                          vmr.geometriaMun = data.geometria;
                          if(data) {
                              validaCep(data, true);
                          }

                          });
                    }
                };

                vm.buscaEnderecoPorCep = function() {
                  var cep = vm.imovel.enderecamento.cep;
                  vm.enderecoDisabled = true;
                    if (cep && cep.length === 8) {
                        ectCepService.get({cep: cep}).$promise.then(successCallback, errorCallback);
                    } else {
                        vm.isCepGenerico = false;
                    }
                };

                function successCallback(data) {
                    vmr.geometriaMun = data.geometria;
                    // if(vmr.geomPonto != undefined){
                    //     if(ciMapaValidacaoFactory.validaCepPontoMunicipio(data.geometria, vmr.geomPonto.getGeometry().getCoordinates())){
                    //         validaCep(data);
                    //     }else {
                    //         MensagensService.exibirMensagemErro("O ponto do imóvel não se encontra sobre o município informado em relação ao CEP.");
                    //         vm.isCepGenerico = false;
                    //         vm.imovel.enderecamento.cep = "";
                    //     }
                    // }else{
                    //     validaCep(data);
                    // }

                    validaCep(data);

                }

                function errorCallback(){
                    vm.isCepGenerico = true;
                    vm.imovel.enderecamento.uf = undefined;
                    vm.imovel.enderecamento.municipio = undefined;
                    vm.imovel.enderecamento.tipoLogradouro = undefined;
                    vm.imovel.enderecamento.logradouro = undefined;
                    vm.imovel.enderecamento.bairro = undefined;
                    vm.enderecoDisabled = false;
                    MensagensService.exibirMensagemErro("CEP não encontrado");
                }

                function validaCep(data, isPrimeiraConsulta){
                    if (isEdicao() && (data.uf !== vm.imovel.enderecamento.uf || data.municipio !== vm.imovel.enderecamento.municipio)) {
                        vm.imovel.enderecamento = vm.imovelOriginal.enderecamento;
                        MensagensService.exibirMensagemErro("Este tipo de alteração não é permitido. Se ela for mesmo necessária, realize o cancelamento e inclua um novo imóvel.");
                    } else {
                        if (possuiPermissaoNaJurisdicao(data.uf)) {
                            data.pais = Dominio.Constantes.Brasil;
                            //essencial na validação do shapeFile
                            vm.imovel.enderecamento.codMunicipio = data.codMunicipio;

                            if(!isPrimeiraConsulta){
                                var idEndereco = vm.imovel.enderecamento.id;
                                vm.imovel.enderecamento = data;
                                if (vm.editandoUA()){
                                  //precisa manter o id do endereço
                                  vm.imovel.enderecamento.id = idEndereco;
                                }
                            }

                            vm.isCepGenerico = data.stLocalidade == 0;
                            if(vm.isCepGenerico)
                              vm.enderecoDisabled = false;

                            CartografiaServiceInc.buscarGeometria(vm.imovel.enderecamento).then(
                                function (result) {
                                    var geometria = result.data ? result.data : data.geometria;
                                    vmr.geometriaCep = result.data ? result.data : data.geometria;
                                    NotificadorService.notify($scope, geometria, notifiers.CENTRALIZAR_POR_MUNICIPIO);
                                }
                            );
                        } else {
                            vm.isCepGenerico = false;
                            vm.imovel.enderecamento = {};
                            vm.imovel.enderecamento.pais = Dominio.Constantes.Brasil;
                            MensagensService.exibirMensagemErro("Você não possui permissão para inserir imóveis nesta UF.");
                        }
                    }
                }

                vm.atualizaCidadeExterior = function (cid) {
                    vm.imovel.enderecamento.cidadeExterior = cid === "Outro" ? null : cid;
                };

                vm.atualizaTipoImovel = function () {
                    tabsIncorporacao.atualizaTabs(vm.imovel.tipoImovel);

                    if (vm.isImovelEspelhoDaguaOuCavidade()) {
                      vm.imovel.enderecamento.cep = null;
                    }
                    if (vm.isImovelEspelhoDagua()) {
                      vm.imovel.naturezaImovel = { id: 3, descricao: "Não se Aplica" };
                    }
                };

                vm.handleTipoImovel = function () {
                    if(isEdicao()){
                        vm.habilitaDemaisOpcoes = false;

                        if(vm.imovel.tipoImovel && (vm.imovel.tipoImovel.id === 1 || vm.imovel.tipoImovel.id === 2)){
                            vm.habilitaOpcaoGleba = true;
                        }else{
                            vm.habilitaOpcaoGleba = false;
                        }
                    }
                };

                vm.handleTipoImovelSelecionado = function () {
                    if(isEdicao() && vm.imovel.tipoImovel && vm.imovel.tipoImovel.id === 1 && vm.imovel.benfeitorias && vm.imovel.benfeitorias.length > 0){
                        vm.confirmaAlterarTipoImovelModalSemEdificacao();
                    }else if(isEdicao() && vm.imovel.tipoImovel && vm.imovel.tipoImovel.id === 2 && (!vm.imovel.benfeitorias || vm.imovel.benfeitorias && vm.imovel.benfeitorias.length == 0)){
                        vm.confirmaAlterarTipoImovelModalComEdificacao();
                    }
                };

                vm.isTipoImovelDisabled = function () {
                    return isDetalhamento();
                };

                vm.isPaisDisabled = function () {
                    return !isCadastro();
                };

                vm.isCepDisabled = function () {
                  if (vm.bloquearInclusaoUA()) return true;
                  return isDetalhamento();
                };

                vm.exibeCep = function () {
                  if (vm.isImovelEspelhoDagua() || vm.isCavidadeNaturalSubterranea()) {
                    return false;
                  }
                  return true;
              };

              vm.buscarMunicipiosPorUf = function (uf2) {
                //vm.imovel.enderecamento.uf = uf2;
                //vm.imovel.enderecamento.ufEspelho = uf2;
                vm.imovel.enderecamento.municipio='';
                vm.municipios = [];
                vm.municipios = MunicipioService.TodosMunicipios.query({ uf: uf2 });
              };

                vm.isInclusao = function () {
                  if (vm.imovel.id) {
                  return false;
                  } else{
                    return true;
                  }
                };

                vm.bloquearInclusaoUA = function(){
                    //Inclusão, UA, terreno reaproveitado
                    if (vm.imovel != null && vm.imovel !=undefined && vm.imovel.tipoImovel != null && vm.imovel.tipoImovel != undefined
                        && vm.imovel.id == null && vm.isTipoUnidadeAutonoma(vm.imovel.tipoImovel)
                        && vm.imovel.enderecamento !=null && vm.imovel.enderecamento.id !=null ){
                      return true;
                    }
                    return false;
                }

                vm.editandoUA = function(){
                  if (vm.imovel != null && vm.imovel !=undefined && vm.imovel.tipoImovel != null && vm.imovel.tipoImovel != undefined
                    && vm.imovel.id != null && vm.isTipoUnidadeAutonoma(vm.imovel.tipoImovel) && !isDetalhamento()){
                      return true;
                    }
                    return false;
                }

                vm.isTipoLogradouroDisabled = function () {
                    if (vm.bloquearInclusaoUA()) return true;
                    //if (vm.editandoUA()) return false;
                    return isBloqueiaCampos();
                };

                vm.isLogradouroDisabled = function () {
                    if (vm.bloquearInclusaoUA()) return true;
                    //if (vm.editandoUA()) return false;
                    return isBloqueiaCampos();
                };

                vm.isBairroDisabled = function () {
                    if (vm.bloquearInclusaoUA()) return true;
                    //if (vm.editandoUA()) return false;
                    return isBloqueiaCampos();
                };

                function isBloqueiaCampos() {
                  if (isDetalhamento()) return true;
                  if(vm.imovel.enderecamento.cep && !vm.isCepGenerico) {
                      return true;
                  }
                  return false;
                }

                vm.isNumeroDisabled = function () {
                  return isDetalhamento();
                };

                vm.isCidadeExteriorDisabled = function () {
                    return !isCadastro();
                };

                vm.isCodigoPostalDisabled = function () {
                    return isDetalhamento();
                };

                vm.isComplementoDisabled = function () {
                    return isDetalhamento();
                };

                vm.isEnderecoDisabled = function () {
                    return isDetalhamento();
                };

                vm.isDadoAlterado = function (propAlterada) {
                    if (isEdicao()) {
                        var propOriginal = propAlterada.replace("imovel", "imovelOriginal"),
                            arrayPropAlterada = propAlterada.split("."),
                            arrayPropOriginal = propOriginal.split("."),
                            dadoAlterado = vm,
                            dadoOriginal = vm,
                            i;

                        for (i = 0; i < arrayPropAlterada.length; i++) {
                            if (angular.isDefined(dadoAlterado) && dadoAlterado !== null) {
                                dadoAlterado = dadoAlterado[arrayPropAlterada[i]];
                            }
                        }
                        for (i = 0; i < arrayPropOriginal.length; i++) {
                            if (angular.isDefined(dadoOriginal) && dadoOriginal !== null) {
                                dadoOriginal = dadoOriginal[arrayPropOriginal[i]];
                            }
                        }

                        return dadoOriginal != dadoAlterado ? "dado-alterado" : "";
                    } else {
                        return "";
                    }
                };

                function possuiPermissaoCadastroUniao() {
                    return vm.usuario.permissoes.includes("CAD_IMOVEL.CADASTRAR_IMOVEL_UNIAO");
                }

                function possuiPermissaoNaJurisdicao(uf) {
                    return vm.usuario.jurisdicoes.includes(uf);
                }

                function isCadastro() {
                    return vm.action === "add";
                }

                function isEdicao() {
                    return vm.action === "edit";
                }

                function isDetalhamento() {
                    return vm.action === "detail";
                }

                vm.confirmaAlterarTipoImovelModalSemEdificacao = function(){

                    var confirm = $mdDialog.confirm({focusOnOpen: false})
                        .textContent("Ao realizar a alteração, os dados cadastrados na aba Acessão/Benfeitoria serão perdidos. Deseja continuar?")
                        .cancel("Não")
                        .ok("Sim");

                    $mdDialog.show(confirm).then(function(){
                        vm.imovel.benfeitorias = [];

                        $mdDialog.hide();
                    }, function(){
                        for(var i = 0; i < vm.tiposImoveis.length; i++){
                            if(vm.imovel.tipoImovel.id === 1 && vm.tiposImoveis[i].id === 2){
                                vm.imovel.tipoImovel = angular.copy(vm.tiposImoveis[i]);
                                return;
                            }
                        }
                        $mdDialog.cancel();
                    });
                };

                vm.confirmaAlterarTipoImovelModalComEdificacao = function(){

                    var confirm = $mdDialog.confirm({focusOnOpen: false})
                        .textContent("Ao realizar a alteração, a aba Acessão/Benfeitoria será exibida para preenchimento. Deseja continuar?")
                        .cancel("Não")
                        .ok("Sim");

                    $mdDialog.show(confirm).then(function(){
                        vm.imovel.benfeitorias = [];

                        $mdDialog.hide();
                    }, function(){
                        for(var i = 0; i < vm.tiposImoveis.length; i++){
                            if(vm.imovel.tipoImovel.id === 2 && vm.tiposImoveis[i].id === 1) {
                                vm.imovel.tipoImovel = angular.copy(vm.tiposImoveis[i]);
                                return;
                            }
                        }
                        $mdDialog.cancel();
                    });
                };

                vm.exibirMsgConfirmacaoEdicao = function(){
                  if (!isDetalhamento() && !vm.jaConfirmou && vm.imovel.id !=null
                    && vm.isTipoUnidadeAutonoma(vm.imovel.tipoImovel)){
                    var confirm = $mdDialog.alert({
                      focusOnOpen: false,
                      textContent: "A edição deste campo atualizará todos os RIPs de unidade autônoma no mesmo terreno. Campos que serão atualizados: CEP, Tipo de Logradouro, Logradouro e Bairro.",
                      ok: "Ok"
                    });
                    vm.jaConfirmou = true;
                    $mdDialog.show(confirm).then(function() {
                    }, function() {

                    });
                  }
                }

                init();
            }
        };
    }
})();

(function () {
    "use strict";
    incInformarCoordenadas.$inject = ["SistemaReferenciaService", "FusoUTMService", "CartografiaServiceInc", "NotificadorService", "MAPA_CONSTANTS_INCORPORACAO", "MensagensService", "ABA_LOCALIZACAO"];
    angular.module("su-cadastro-imovel").directive("incInformarCoordenadas", incInformarCoordenadas);

    function incInformarCoordenadas(SistemaReferenciaService, FusoUTMService, CartografiaServiceInc, NotificadorService, MAPA_CONSTANTS_INCORPORACAO, MensagensService, ABA_LOCALIZACAO) {
        return {
            retrict: "E",
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/localizacao/templates/inc-informar-coordenadas.html",
            scope: {
                imovel: "=",
                desabilitar: "=",
                action: "@"
            },
            link: function ($scope) {

                var vm = $scope,
                    notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES,
                    WGS84_WEB_MERCATOR = 3857;

                vm.sistemasReferencia = [];
                vm.fusosUTM = [];
                vm.grauMinutoSegundo = null;
                vm.grauDecimal = null;
                vm.metricas = null;
                vm.submittedCoodenadas = false;

                function init() {
                    SistemaReferenciaService.buscarTodos().then(function (result) {
                        vm.sistemasReferencia = result.data;
                        angular.forEach(result.data, function (sistema) {
                          if (sistema.codigoSistemico === 'INC_FORM_LOC_SIRGAS_2000') {
                            vm.imovel.sistemaReferencia = sistema;
                          }
                      });
                    });

                    FusoUTMService.buscarTodos().then(function (result) {
                        vm.fusosUTM = result.data;
                    });

                    vm.grauMinutoSegundo = novoGMS();
                    vm.grauDecimal = novoGD();
                    vm.metricas = novoM();

                    if (vm.imovel.pontoImovel && vm.possuiSistemaReferencia()) {
                        atualizarInformacoesCoordenadas(vm.imovel.pontoImovel.coordinates)
                    }
                }

                function novoGMS() {
                    return {
                        latitude: {
                            hemisferio: null,
                            graus: null,
                            minutos: null,
                            segundos: null
                        },
                        longitude: {
                            hemisferio: null,
                            graus: null,
                            minutos: null,
                            segundos: null
                        }
                    };
                }

                function novoGD() {
                    return {
                        latitude: {
                            hemisferio: null,
                            decimal: null
                        },
                        longitude: {
                            hemisferio: null,
                            decimal: null
                        }
                    };
                }

                function novoM() {
                    return {
                        coordenadaE: null,
                        coordenadaN: null,
                        fusoUTM: null
                    };
                }

                function atualizarInformacoesCoordenadas(coordenadas) {
                    CartografiaServiceInc.transformarGeometria(WGS84_WEB_MERCATOR, vm.imovel.sistemaReferencia.codigo, coordenadas[0], coordenadas[1]).then(
                        function (result) {
                            vm.grauDecimal.longitude.decimal = result.data.coordinates[0];
                            vm.grauDecimal.longitude.hemisferio = vm.grauDecimal.longitude.decimal < 0 ? "O" : "L";

                            vm.grauDecimal.latitude.decimal = result.data.coordinates[1];
                            vm.grauDecimal.latitude.hemisferio = vm.grauDecimal.latitude.decimal < 0 ? "S" : "N";

                            vm.grauMinutoSegundo = CartografiaServiceInc.gdToGms(vm.grauDecimal);
                            vm.metricas = CartografiaServiceInc.gdToUtm(vm.grauDecimal);
                        }
                    );
                }

                NotificadorService.subscribe(vm, function (evento, escopo, coordenadas) {
                    if (vm.possuiSistemaReferencia()) {
                        atualizarInformacoesCoordenadas(coordenadas);
                    }
                }, notifiers.ATUALIZAR_COORDENADAS_DO_PONTO);

                NotificadorService.subscribe(vm, function(evento, escopo, tabIndex) {
                  if (tabIndex == ABA_LOCALIZACAO){
                    vm.sistemasReferencia = [];
                    vm.fusosUTM = [];
                    vm.grauMinutoSegundo = novoGMS();
                    vm.grauDecimal = novoGD();
                    vm.metricas = novoM();
                    vm.submittedCoodenadas = false;
                  }

                }, notifiers.LIMPAR_ABA);

                vm.possuiSistemaReferencia = function() {
                    return vm.imovel.sistemaReferencia && vm.imovel.sistemaReferencia.codigo;
                };

                vm.onChangeHemisferioLatitude = function() {
                    if (vm.grauDecimal.latitude.hemisferio === "S") {
                        if (vm.grauDecimal.latitude.decimal && parseFloat(vm.grauDecimal.latitude.decimal) !== 0) {
                            vm.grauDecimal.latitude.decimal = Math.abs(vm.grauDecimal.latitude.decimal) * -1;
                        }
                    } else if (vm.grauDecimal.latitude.hemisferio === "N") {
                        vm.grauDecimal.latitude.decimal = Math.abs(vm.grauDecimal.latitude.decimal);
                    }
                };

                vm.onChangeHemisferioLongitude = function() {
                    if (vm.grauDecimal.longitude.hemisferio === "O") {
                        if (vm.grauDecimal.longitude.decimal && parseFloat(vm.grauDecimal.longitude.decimal) !== 0) {
                            vm.grauDecimal.longitude.decimal = Math.abs(vm.grauDecimal.longitude.decimal) * -1;
                        }
                    } else if (vm.grauDecimal.longitude.hemisferio === "L") {
                        vm.grauDecimal.longitude.decimal = Math.abs(vm.grauDecimal.longitude.decimal);
                    }
                };

                vm.marcarPonto = function () {
                    if (vm.coordenadasForm.$valid) {
                        if (vm.imovel.formatoCoordenadas === "M") {
                            vm.grauDecimal = CartografiaServiceInc.utmToGd(vm.metricas);
                            vm.grauMinutoSegundo = CartografiaServiceInc.gdToGms(vm.grauDecimal);
                        } else if (vm.imovel.formatoCoordenadas === "GMS") {
                            vm.grauDecimal = CartografiaServiceInc.gmsToGd(vm.grauMinutoSegundo);
                            vm.metricas = CartografiaServiceInc.gdToUtm(vm.grauDecimal);
                        } else {
                            vm.metricas = CartografiaServiceInc.gdToUtm(vm.grauDecimal);
                            vm.grauMinutoSegundo = CartografiaServiceInc.gdToGms(vm.grauDecimal);
                        }

                        CartografiaServiceInc.transformarGeometria(vm.imovel.sistemaReferencia.codigo, WGS84_WEB_MERCATOR, vm.grauDecimal.longitude.decimal, vm.grauDecimal.latitude.decimal).then(
                            function (result) {
                                NotificadorService.notify(vm, result.data, notifiers.MARCAR_PONTO_POR_COORDENADAS);
                            }
                        );
                    } else {
                        vm.coordenadasForm.$setSubmitted();
                        vm.submittedCoodenadas = true;
                        MensagensService.exibirMensagemCamposObrigatorios();
                    }
                };

                vm.isSistemaReferenciaDisabled = function () {
                    return isDetalhamento();
                };

                vm.isGrauDecimalDisabled = function () {
                    return isDetalhamento();
                };

                vm.isMetricasDisabled = function () {
                    return isDetalhamento();
                };

                vm.isGrauMinutoSegundoDisabled = function () {
                    return isDetalhamento();
                };

                vm.esconderBotaoMarcarPonto = function () {
                    return isDetalhamento();
                };

                function isDetalhamento() {
                    return vm.action === 'detail';
                }

                init();
            }
        }
    }
})();

(function () {
  'use strict';
  fieldsetTituloAquisitivo.$inject = ["$mdDialog", "PaginacaoFactory"];
  angular
    .module('su-cadastro-imovel')
    .directive('fieldsetTituloAquisitivo', fieldsetTituloAquisitivo);

  function fieldsetTituloAquisitivo($mdDialog, PaginacaoFactory) {
    return {
      templateUrl:
        'scripts-cadastro-imovel/app/directives/secoes-formulario/fieldset-titulo-aquisitivo/templates/fieldset-titulo-aquisitivo.html',
      require: '^secaoDadosIncorporacao',
      link: function ($scope, element, attrs, ctrlDadosIncorporacao) {
        var vm = $scope;

        vm.ordenacao = null;
        vm.paginacao = PaginacaoFactory.getDefault();

        vm.exibirTabTituloAquisitivo = function (tituloAquisitivo) {
          ctrlDadosIncorporacao.exibeTab(true, tituloAquisitivo);
        };

        vm.isDetalhar = function () {
          return ctrlDadosIncorporacao.action === 'detail' || attrs.isDetail === 'true';
        };
        vm.confirmarExclusaoTitulo = function (titulo, ev) {
          var confirm = $mdDialog
            .confirm({
              focusOnOpen: false,
            })
            .textContent('Deseja remover este título aquisitivo?')
            .targetEvent(ev)
            .cancel('Não')
            .ok('Sim');

          $mdDialog.show(confirm).then(function () {
            var index = ctrlDadosIncorporacao.titulos.indexOf(titulo);
            ctrlDadosIncorporacao.titulos.splice(index, 1);
          });
        };

        vm.getDescricao = function (titulo) {
          if (!titulo) {
            return '';
          }
          return (
            titulo.tipoTituloAquisitivo.descricao +
            ' / ' +
            titulo.tipoAtoLegal.descricao
          );
        };
      },
    };
  }
})();

(function () {
    "use strict";
    fieldsetEncargos.$inject = ["$mdDialog", "MensagensService", "PaginacaoFactory"];
    angular.module("su-cadastro-imovel").directive("fieldsetEncargos", fieldsetEncargos);

    function fieldsetEncargos($mdDialog, MensagensService, PaginacaoFactory) {

        return{
            templateUrl:"scripts-cadastro-imovel/app/directives/secoes-formulario/encargos-fieldset/templates/encargos-fieldset.html",
            scope:{
                titulo:"=",
                imovel:"=",
                detail: '='
            },
            link:function ($scope) {

                var vm = $scope;

                vm.checkboxData = {
                    valor : false
                };
                vm.submittedEncargos = false;
                vm.encargo = {};
                vm.botaoEncargo = "Adicionar";
                vm.indexEdicaoEncargo = undefined;
                vm.ordenacao = null;
                vm.paginacao = PaginacaoFactory.getDefault();

                vm.isDetalhar = function () {
                  return vm.detail;
                };

                vm.adicionarEncargo = function() {
                    if (vm.encargo.descricao && vm.encargo.prazoCumprimento && vm.encargo.cumprimento) {
                        vm.encargo.valorCheckBox = vm.checkboxData.valor;

                        if (angular.isDefined(vm.indexEdicaoEncargo)) {
                            vm.titulo.encargos[vm.indexEdicaoEncargo] = angular.copy(vm.encargo);
                            vm.indexEdicaoEncargo = undefined;
                        } else {
                            vm.titulo.encargos.push(vm.encargo);
                        }
                        vm.submittedEncargos = false;
                        vm.encargo = {};
                        vm.botaoEncargo = "Adicionar";
                        if (vm.checkboxData.valor) {
                            vm.checkboxData.valor = false;
                        }
                    } else {
                        vm.submittedEncargos = true;
                        MensagensService.exibirMensagemCamposObrigatorios();
                    }
                };

                vm.$watch('titulo.dataFinalVigencia', function () {
                    if (vm.checkboxData.valor == true) {
                        copiaDataFinalParaCumprimento();
                    }
                });

                vm.limparEncargoForm = function() {
                    vm.encargo = undefined;
                };

                vm.editarEncargo = function (encargo) {
                    vm.encargo = angular.copy(encargo);
                    vm.checkboxData.valor = vm.encargo.valorCheckBox;
                    vm.botaoEncargo = "Salvar";
                    vm.indexEdicaoEncargo = vm.titulo.encargos.indexOf(encargo);
                };

                vm.excluirEncargo = function (ev, encargo) {
                    var confirm = $mdDialog.confirm({
                        focusOnOpen: false
                    })
                        .textContent("Deseja remover este encargo?")
                        .targetEvent(ev)
                        .cancel("Não")
                        .ok("Sim");

                    $mdDialog.show(confirm).then(
                        function () {
                            var index = vm.titulo.encargos.indexOf(encargo);
                            vm.titulo.encargos.splice(index, 1);
                            vm.page();
                        }
                    );
                };

                vm.utilizarDataFinalParaCumprimento = function () {
                    if(vm.checkboxData.valor == true){
                        copiaDataFinalParaCumprimento();
                    }else{
                        vm.encargo.prazoCumprimento = undefined;
                    }
                };

                function copiaDataFinalParaCumprimento() {
                    vm.encargo.prazoCumprimento = angular.copy(vm.titulo.dataFinalVigencia);
                }
            }
        }

    }


}());

(function () {
    "use strict";
        fieldsetCertidoes.$inject = ["$mdDialog", "MensagensService"];
    angular.module("su-cadastro-imovel").directive("fieldsetCertidoes", fieldsetCertidoes);

    function fieldsetCertidoes($mdDialog, MensagensService) {

        return {

            templateUrl:"scripts-cadastro-imovel/app/directives/secoes-formulario/documentos-cartoriais/templates/documentos-cartoriais.html",
            scope: {
                imovel:"="
            },
            link:function ($scope, el, attrs, ctrl) {

                var vm = $scope;

                vm.dados = {};

                vm.imovel.listaDocumentosCartoriais = [];

                vm.adicionaDocumento = function () {
                    if(vm.dados.nome && vm.dados.cartorio && vm.dados.arquivo){
                    vm.imovel.listaDocumentosCartoriais.push(vm.dados);
                    vm.dados = {};
                    }
                    else {
                        MensagensService.exibirMensagemCamposObrigatorios()
                    }
                };

                vm.confirmaRemocao = function(ev,documento){
                    var confirm = $mdDialog.confirm({focusOnOpen: false})
                        .textContent("Deseja realmente remover esta matrícula?")
                        .ariaLabel("Remover")
                        .cancel("Não")
                        .ok("Remover");

                    $mdDialog.show(confirm).then(function(){
                        removaDocumento(documento);
                        $mdDialog.hide();
                    }, function(){
                        $mdDialog.cancel();
                    });

                };

                 var removaDocumento = function(documento){
                    var _index = vm.imovel.listaDocumentosCartoriais.indexOf(documento);
                    vm.imovel.listaDocumentosCartoriais.splice(_index, 1);
                };

                }
            }

        }




}());

(function () {
  'use strict';
  detalhesImovel.$inject = ["$mdDialog", "$filter", "NotificadorService", "Dominio", "MAPA_CONSTANTS_INCORPORACAO", "ValidaAreaTerrenoService", "ValidaFracaoIdealTerrenoService", "MensagensService", "ABA_TERRENO"];
  angular
    .module('su-cadastro-imovel')
    .directive('descricaoTerrenoCartorial', detalhesImovel);

  function detalhesImovel(
    $mdDialog,
    $filter,
    NotificadorService,
    Dominio,
    MAPA_CONSTANTS_INCORPORACAO,
    ValidaAreaTerrenoService,
    ValidaFracaoIdealTerrenoService,
    MensagensService,
    ABA_TERRENO
  ) {
    return {
      templateUrl:
        'scripts-cadastro-imovel/app/directives/secoes-formulario/descricao-terreno-cartorial/templates/descricao-terreno-cartorial.html',
      scope: {
        imovel: '=',
        tiposGeometria: '=',
        action: '=',
        desabilitar: '='
      },
      link: function ($scope) {
        var vm = $scope,
          notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES,
          featureIds = MAPA_CONSTANTS_INCORPORACAO.FEATURE_ID,
          PROPRIETARIO_REAL_UNIAO = 1,
          TIPO_IMOVEL_UNIDADE_AUTONOMA = 4,
          POR_FRACIONAMENTO = 3;

        vm.TIPO_DESENHO = Dominio.Constantes.TiposDeDesenho;
        vm.terreno = vm.imovel.terreno;
        vm.jaConfirmou = false;

        vm.unidadeAutonoma = vm.imovel.unidadeAutonoma;
        vm.msgShapefileTitulo = 'Importar Shapefile';
        vm.msgShapefileLines = [
          'O arquivo shapefile deve estar compactado em um arquivo .zip',
          'O arquivo .zip deve conter pelo menos os componentes dos arquivos .shp, .shx, .dbf (ou .qpj) e .prj do shapefile.',
          'Deverá ser adotado sistema geodésico de referência SIRGAS2000;',
          'Podendo o arquivo shapefile ser apresentado em coordenadas geográficas (EPSG:4674) ou e, coordenadas planas na projeção UTM.',
          'O arquivo shapefile deve pertencer ao mesmo município do imóvel.',
        ];
        // vm.msgTxtCsvTitulo = 'Importar TXT e CSV';
        //vm.msgTxtCsvLines = [
        //  'O arquivo .txt e .csv deve ser apresentado da seguinte forma:',
        //  'EX:',
        //  'Área do terreno a partir de levantamento físico-cadastral, Datum sirgas 2000 coordenadas geográficas',
        //  'coordenadas (x,y):',
        //  '-20,459722 15,583330',
        //  '-20,459722 15,683330',
        //  '-20,459722 15,783330',
        //  '-20,459722 15,783330',
       // ];

       vm.msgTxtCsvTitulo ='';
       vm.msgTxtCsvLines = ['','']

        vm.alterarTipoDesenho = function (ev) {
          var confirm, newValue, oldValue;

          newValue = vm.terreno.tipoGeometriaCartorial;

          if (newValue === vm.TIPO_DESENHO.NAO_GEORREFERENCIADA) {
            oldValue = vm.TIPO_DESENHO.ARQUIVO;
          } else {
            oldValue = vm.TIPO_DESENHO.NAO_GEORREFERENCIADA;
          }

          if (
            newValue === vm.TIPO_DESENHO.NAO_GEORREFERENCIADA &&
            vm.terreno.geometriaTerrenoCartorial
          ) {
            confirm = getDialogConfirmarAlteracaoTipoDesenho(ev, newValue);

            $mdDialog.show(confirm).then(
              function () {
                //SIM
                vm.limparDadosDescricaoCartorial();
              },
              function () {
                //NÃO
                vm.terreno.tipoGeometriaCartorial = oldValue;
              }
            );
          } else if (newValue === vm.TIPO_DESENHO.ARQUIVO) {
            if (
              vm.terreno.geometriaTerreno &&
              isEmpty(vm.terreno.arquivoCadastral)
            ) {
              confirm = getDialogConfirmarAlteracaoTipoDesenho(ev, newValue);

              $mdDialog.show(confirm).then(
                function () {
                  //SIM
                  vm.limparDadosDescricaoCadastral();
                  vm.terreno.tipoGeometriaCadastral = vm.TIPO_DESENHO.ARQUIVO;
                },
                function () {
                  //NÃO
                  vm.terreno.tipoGeometriaCartorial = oldValue;
                }
              );
            } else {
              vm.terreno.tipoGeometriaCadastral = vm.TIPO_DESENHO.ARQUIVO;
            }
          }
        };

        vm.formatarNumero = function (fracaoIdeal) {
          vm.imovel.unidadeAutonoma.fracaoIdealCartorial = fracaoIdeal.replace(
            /[^\d]+/g,
            ''
          );
          var stringNumero = fracaoIdeal.replace(/[^\d]+/g, '');
          var inteiro = stringNumero.slice(0, 1);
          var decimal = stringNumero.slice(1, stringNumero.length);
          var numero = 0;
          var decimalSeparator = '.';
          var stringlSeparator = ',';

          if (stringNumero.length > 1) {
            numero = inteiro + decimalSeparator + decimal;
          } else {
            numero = stringNumero;
          }

          if (numero > 1) {
            vm.imovel.unidadeAutonoma.fracaoIdealCartorial = undefined;
          } else if (numero == 1 && numero.length == 1) {
            var float = parseFloat(numero);
            vm.imovel.unidadeAutonoma.fracaoIdealCartorial = $filter('number')(
              float,
              9
            );
            $scope.fracaoIdealCartorialNum = float.toFixed(9);
            $rootScope.fracaoIdealCartorialNum = $scope.fracaoIdealCartorialNum;
          } else if (numero[0] == 0 && numero.length > 1) {
            vm.imovel.unidadeAutonoma.fracaoIdealCartorial =
              inteiro + stringlSeparator + decimal;
            $scope.fracaoIdealCartorialNum = parseFloat(
              inteiro + decimalSeparator + decimal
            );
            $rootScope.fracaoIdealCartorialNum = $scope.fracaoIdealCartorialNum;
          }
        };

        vm.isNaoGeoreferenciada = function () {
          // eslint-disable-next-line angular/log

          return (
            vm.terreno.tipoGeometriaCartorial ===
            vm.TIPO_DESENHO.NAO_GEORREFERENCIADA
          );
        };

        function isEmpty(value) {
          return (
            angular.equals({}, value) ||
            angular.isUndefined(value) ||
            value === null ||
            value === ''
          );
        }

        function getDialogConfirmarAlteracaoTipoDesenho(ev, tipoDesenho) {
          var msg;

          if (tipoDesenho === vm.TIPO_DESENHO.ARQUIVO) {
            msg =
              'Não será permitido um polígono com baixa precisão da geometria cadastral. Os dados informados da vetorização em tela serão perdidos. Deseja confirmar?';
          } else {
            msg =
              'Já existe uma geometria enviada por meio de um arquivo. Deseja realmente excluir a geometria existente?';
          }

          return $mdDialog
            .confirm({ focusOnOpen: false })
            .title('')
            .textContent(msg)
            .targetEvent(ev)
            .ok('Sim')
            .cancel('Não');
        }

        vm.isImovelUniaoParcialmente = function () {
          return (
            vm.imovel &&
            vm.imovel.proprietario &&
            vm.imovel.proprietario.tipoProprietarioReal &&
            vm.imovel.proprietario.tipoProprietarioReal.id ===
            PROPRIETARIO_REAL_UNIAO &&
            vm.imovel.proprietario.isIntegralmente == 'true'
          );
        };

        vm.isImovelUnidadeAutonoma = function () {
          return (
            vm.imovel &&
            vm.imovel.tipoImovel &&
            vm.imovel.tipoImovel.tipoPai === 'UNIDADE AUTÔNOMA'
          );
        };

        vm.limparDadosDescricaoCadastral = function () {
          vm.terreno.memorialDescritivoCadastral = undefined;
          vm.terreno.areaTotalCadastral = undefined;
          vm.terreno.areaUniaoCadastral = undefined;
          if (vm.unidadeAutonoma) {
            vm.unidadeAutonoma.fracaoIdealCadastral = undefined;
          }
          vm.terreno.fracaoIdealUniaoCadastral = undefined;
          vm.terreno.geometriaTerreno = undefined;
          vm.terreno.arquivoCadastral = {};
          vm.terreno.areaShapeFileCadastral = undefined;

          NotificadorService.notify(
            $scope,
            featureIds.TERRENO_CADASTRAL,
            notifiers.LIMPAR_DESENHO
          );
        };

        vm.limparDadosDescricaoCartorial = function () {
          vm.terreno.memorialDescritivoCartorial = undefined;
          if (!vm.isTipoIncorporacaoPorFusao()) {
            vm.terreno.areaTotalCartorial = undefined;
          }
          vm.terreno.areaUniaoCartorial = undefined;
          if (vm.unidadeAutonoma) {
            vm.unidadeAutonoma.fracaoIdealCartorial = undefined;
          }
          vm.terreno.fracaoIdealUniaoCartorial = undefined;
          vm.terreno.geometriaTerrenoCartorial = undefined;
          vm.terreno.arquivoCartorial = {};
          vm.terreno.areaShapeFileCartorial = undefined;

          NotificadorService.notify(
            $scope,
            featureIds.TERRENO_CARTORIAL,
            notifiers.LIMPAR_DESENHO
          );
        };

        vm.isTipoIncorporacaoPorFusao = function () {
          if (
            vm.imovel.incorporacao &&
            vm.imovel.incorporacao.tipoIncorporacao
          ) {
            return vm.imovel.incorporacao.tipoIncorporacao.id === 4;
          }
        };

        vm.salvarDadosDescricaoCartorial = function (data) {
          vm.terreno.arquivoCartorial = vm.terreno.arquivoCartorial ? vm.terreno.arquivoCartorial : {};
          vm.terreno.arquivoCartorial.nome = data.arquivo.nome;
          vm.terreno.arquivoCartorial.arquivo = data.arquivo.arquivo;
          vm.terreno.arquivoCartorial.dataPlanta = data.arquivo.dataPlanta;
          vm.terreno.arquivoCartorial.escala = data.arquivo.escala;
          vm.terreno.arquivoCartorial.tipoFormaLevantamento =
            data.arquivo.tipoFormaLevantamento;
          vm.terreno.arquivoCartorial.resolucaoEspacial =
            data.arquivo.resolucaoEspacial;
          vm.terreno.arquivoCartorial.tipoEquipamento =
            data.arquivo.tipoEquipamento;
          vm.terreno.memorialDescritivoCartorial = data.memorialDescritivo;
          vm.terreno.areaTotalCartorial = data.areaTotal;
          vm.terreno.areaShapeFileCartorial = data.areaTotal;
          vm.terreno.geometriaTerrenoCartorial = data.geometria;

          NotificadorService.notify(
            $scope,
            {
              feature: data.geometria,
              tipoTerreno: featureIds.TERRENO_CARTORIAL,
            },
            notifiers.UPLOAD_SHAPEFILE
          );
        };

        vm.validarAreaTotal = function () {
          if (
            vm.imovel.incorporacao.tipoIncorporacao.id === POR_FRACIONAMENTO &&
            vm.imovel.terreno.areaTotalCartorial
          ) {
            ValidaAreaTerrenoService.get({
              rip: vm.imovel.incorporacao.codigoOrigem,
              area: vm.imovel.terreno.areaTotalCartorial,
            }).$promise.then(function (response) {
              if (!response.isValid) {
                vm.imovel.terreno.areaTotalCartorial = undefined;
                MensagensService.exibirMensagemErro('Área total inválida');
              }
            });
          }
        };

        vm.validarAreaUniao = function () {
          if (
            vm.imovel.terreno.areaUniaoCartorial &&
            vm.imovel.terreno.areaTotalCartorial
          ) {
            if (
              vm.imovel.terreno.areaTotalCartorial <
              vm.imovel.terreno.areaUniaoCartorial
            ) {
              vm.imovel.terreno.areaUniaoCartorial = undefined;
              MensagensService.exibirMensagemErro(
                'A Área da União não pode ser maior que a Área Total.'
              );
            }
          }
        };

        vm.alteraMha = function () {

          if (vm.imovel.terreno.unidadeMedidaCartorial == 'ha') {
                vm.imovel.terreno.areaTotalCartorial = vm.imovel.terreno.areaTotalCartorial / 100;
          } else if (vm.imovel.terreno.unidadeMedidaCartorial == 'm2') {
                vm.imovel.terreno.areaTotalCartorial = vm.imovel.terreno.areaTotalCartorial * 100;
          }

        };

        vm.validarFracaoIdealImovel = function () {
          if (vm.imovel.unidadeAutonoma.fracaoIdealCartorial === 0) {
            vm.imovel.unidadeAutonoma.fracaoIdealCartorial = undefined;
            MensagensService.exibirMensagemErro(
              'A Fração Ideal do Imóvel deve ser maior que 0.'
            );
          } else {
            if (
              vm.imovel.unidadeAutonoma.fracaoIdealCartorial &&
              vm.imovel.terreno.areaTotalCartorial
            ) {
              if (
                vm.imovel.terreno.areaTotalCartorial <
                vm.imovel.unidadeAutonoma.fracaoIdealCartorial
              ) {
                vm.imovel.unidadeAutonoma.fracaoIdealCartorial = undefined;
                MensagensService.exibirMensagemErro(
                  'A Fração Ideal do Imóvel deve ser inferior à Área Total.'
                );
              } else if (
                vm.isImovelUnidadeAutonoma() &&
                vm.isImovelUniaoParcialmente()
              ) {
                if (
                  vm.imovel.unidadeAutonoma.fracaoIdealCartorial >=
                  vm.imovel.terreno.areaUniaoCartorial
                ) {
                  vm.imovel.unidadeAutonoma.fracaoIdealCartorial = undefined;
                  MensagensService.exibirMensagemErro(
                    'A Fração Ideal do Imóvel deve ser inferior à Área da União.'
                  );
                }
              }
            }

            if (vm.unidadeAutonoma.fracaoIdealCartorial && vm.terreno.id) {
              ValidaFracaoIdealTerrenoService.get({
                idTerreno: vm.terreno.id,
                ripImovel: vm.imovel.rip,
                area: vm.unidadeAutonoma.fracaoIdealCartorial,
              }).$promise.then(function (response) {
                if (!response.isValid) {
                  vm.unidadeAutonoma.fracaoIdealCartorial = undefined;
                  MensagensService.exibirMensagemErro(
                    'A soma das frações ideias das unidades autônomas desse terreno é superior a área do terreno. Favor informar outro valor de Fração Ideal do Imóvel.'
                  );
                }
              });
            }
          }
        };

        vm.calcularAreaFracaoIdealUniaoCartorial = function () {
          vm.imovel.unidadeAutonoma.fracaoIdealCartorial *
            (vm.imovel.terreno.areaUniaoCartorial /
              vm.imovel.terreno.areaTotalCartorial);
        };

        vm.desabilitarMemorialDescritivo = function () {
          return (
            !vm.imovel.terreno.geometriaTerrenoCartorial/* ||
            ((isFormaLevantamentoImagensSensoriais() ||
              isFormaLevantamentoAerofotogametria() ||
              isTipoFormaLevantamentoGoogle()) &&
              vm.imovel.terreno.arquivoCartorial.resolucaoEspacial <= 0.3) ||
            (isFormaLevantamentoTopografico() &&
              !isTipoEquipamentoGPSNavegacao())*/
          );
        };

        vm.isDetalhar = function () {
          return vm.action === 'detail';
        };

        vm.isAlterar = function () {
          return vm.action === 'edit';
        };

        function isFormaLevantamentoImagensSensoriais() {
          if (
            vm.imovel.terreno.arquivoCartorial &&
            vm.imovel.terreno.arquivoCartorial.tipoFormaLevantamento
          ) {
            return (
              vm.imovel.terreno.arquivoCartorial.tipoFormaLevantamento.id === 1
            );
          }
          return false;
        }

        function isFormaLevantamentoAerofotogametria() {
          if (
            vm.imovel.terreno.arquivoCartorial &&
            vm.imovel.terreno.arquivoCartorial.tipoFormaLevantamento
          ) {
            return (
              vm.imovel.terreno.arquivoCartorial.tipoFormaLevantamento.id === 2
            );
          }
          return false;
        }

        function isFormaLevantamentoTopografico() {
          if (
            vm.imovel.terreno.arquivoCartorial &&
            vm.imovel.terreno.arquivoCartorial.tipoFormaLevantamento
          ) {
            return (
              vm.imovel.terreno.arquivoCartorial.tipoFormaLevantamento.id === 3
            );
          }
          return false;
        }

        function isTipoEquipamentoGPSNavegacao() {
          if (
            vm.imovel.terreno.arquivoCartorial &&
            vm.imovel.terreno.arquivoCartorial.tipoEquipamento
          ) {
            return vm.imovel.terreno.arquivoCartorial.tipoEquipamento.id === 2;
          }
          return false;
        }

        function isTipoFormaLevantamentoGoogle() {
          if (
            vm.imovel.terreno.arquivoCartorial &&
            vm.imovel.terreno.arquivoCartorial.tipoFormaLevantamento
          ) {
            return (
              vm.imovel.terreno.arquivoCartorial.tipoFormaLevantamento.id === 4
            );
          }
        }

        NotificadorService.subscribe(vm, function(evento, escopo, tabIndex) {
          if (tabIndex == ABA_TERRENO){
            vm.terreno = {};
          }
        }, notifiers.LIMPAR_ABA);

        vm.exibirMsgConfirmacaoEdicao = function(){
          if (vm.isImovelUnidadeAutonoma() && !vm.jaConfirmou && vm.imovel.id !=null  && !vm.isDetalhar()){
            var confirm = $mdDialog.alert({
              focusOnOpen: false,
              textContent: "A edição desse campo atualiza ao mesmo tempo a informação em todos os RIPs de unidade autônoma localizados no mesmo terreno. Todos os campos dessa sessão serão atualizados.",
              ok: "Ok"
            });
            vm.jaConfirmou = true;
            $mdDialog.show(confirm).then(function() {
            }, function() {
            });
          }
        }

      },
    };
  }
})();

(function () {
  'use strict';
  detalhesImovel.$inject = ["$mdDialog", "$filter", "$rootScope", "NotificadorService", "Dominio", "MAPA_CONSTANTS_INCORPORACAO", "ValidaAreaTerrenoService", "ValidaFracaoIdealTerrenoService", "MensagensService", "ABA_TERRENO", "ABA_LOCALIZACAO"];
  angular
    .module('su-cadastro-imovel')
    .directive('descricaoTerrenoCadastral', detalhesImovel);

  function detalhesImovel(
    $mdDialog,
    $filter,
    $rootScope,
    NotificadorService,
    Dominio,
    MAPA_CONSTANTS_INCORPORACAO,
    ValidaAreaTerrenoService,
    ValidaFracaoIdealTerrenoService,
    MensagensService,
    ABA_TERRENO,
    ABA_LOCALIZACAO
  ) {
    return {
      templateUrl:
        'scripts-cadastro-imovel/app/directives/secoes-formulario/descricao-terreno-cadastral/templates/descricao-terreno-cadastral.html',
      scope: {
        imovel: '=',
        tiposGeometria: '=',
        action: '=',
        desabilitar: '='
      },
      link: function ($scope) {
        var vm = $scope,
          notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES,
          featureIds = MAPA_CONSTANTS_INCORPORACAO.FEATURE_ID,
          PROPRIETARIO_REAL_UNIAO = 1,
          POR_FRACIONAMENTO = 3,
          POR_FUSAO = 4;

        vm.jaConfirmou = false;

        vm.TIPO_DESENHO = Dominio.Constantes.TiposDeDesenho;
        vm.terreno = vm.imovel.terreno;
        vm.unidadeAutonoma = vm.imovel.unidadeAutonoma;
        vm.msgShapefileTitulo = 'Importar Shapefile';
        vm.msgShapefileLines = [
          'O arquivo shapefile deve estar compactado em um arquivo .zip',
          'O arquivo .zip deve conter pelo menos os componentes dos arquivos .shp, .shx, .dbf (ou .qpj) e .prj do shapefile.',
          'Deverá ser adotado sistema geodésico de referência SIRGAS2000;',
          'Podendo o arquivo shapefile ser apresentado em coordenadas geográficas (EPSG:4674) ou e, coordenadas planas na projeção UTM.',
          'O arquivo shapefile deve pertencer ao mesmo município do imóvel.',
        ];
       // vm.msgTxtCsvTitulo = 'Importar TXT e CSV';
        //vm.msgTxtCsvLines = [
        //  'O arquivo .txt e .csv deve ser apresentado da seguinte forma:',
        //  'EX:',
        //  'Área do terreno a partir de levantamento físico-cadastral, Datum sirgas 2000 coordenadas geográficas',
        //  'coordenadas (x,y):',
        //  '-20,459722 15,583330',
        //  '-20,459722 15,683330',
        //  '-20,459722 15,783330',
        //  '-20,459722 15,783330',
       // ];
       vm.msgTxtCsvTitulo ='';
       vm.msgTxtCsvLines = ['','']
        if (vm.unidadeAutonoma && vm.unidadeAutonoma.fracaoIdealCadastral) {
          vm.unidadeAutonoma.fracaoIdealCadastral = $filter('number')(
            vm.unidadeAutonoma.fracaoIdealCadastral,
            9
          );
        }

        if (vm.unidadeAutonoma && vm.unidadeAutonoma.fracaoIdealCartorial) {
          vm.unidadeAutonoma.fracaoIdealCartorial = $filter('number')(
            vm.unidadeAutonoma.fracaoIdealCartorial,
            9
          );
        }

        vm.alterarTipoDesenho = function (ev) {
          if (vm.terreno.geometriaTerreno) {
            var confirm, newValue, oldValue;

            newValue = vm.terreno.tipoGeometriaCadastral;

            if (vm.terreno.tipoGeometriaCadastral === vm.TIPO_DESENHO.VETORIZACAO) {
              oldValue = vm.TIPO_DESENHO.ARQUIVO;
            } else {
              oldValue = vm.TIPO_DESENHO.VETORIZACAO;
            }

            confirm = getDialogConfirmarAlteracaoTipoDesenho(ev, newValue);

            $mdDialog.show(confirm).then(
              function () {
                //SIM
                vm.limparDadosDescricaoCadastral();
                if (vm.terreno.tipoGeometriaCadastral === vm.TIPO_DESENHO.VETORIZACAO) {
                  NotificadorService.notify($scope, null, 'bloquearTelaTerreno');
                }
              },
              function () {
                //NÃO
                vm.terreno.tipoGeometriaCadastral = oldValue;
              }
            );
          } else {
            if (vm.terreno.tipoGeometriaCadastral === vm.TIPO_DESENHO.VETORIZACAO) {
              NotificadorService.notify($scope, null, 'bloquearTelaTerreno');
            }
          }
        };

        vm.isVetorizacao = function () {
          return (
            vm.terreno.tipoGeometriaCadastral === vm.TIPO_DESENHO.VETORIZACAO
          );
        };

        function getDialogConfirmarAlteracaoTipoDesenho(ev, tipoDesenho) {
          var msg;

          if (tipoDesenho === vm.TIPO_DESENHO.ARQUIVO) {
            msg =
              'Já existe uma geometria vetorizada em tela para o terreno. Deseja realmente excluir a geometria para o envio de um arquivo?';
          } else {
            msg =
              'Já existe uma geometria enviada por meio de um arquivo. Deseja realmente excluir a geometria existente por uma vetorização em tela?';
          }

          return $mdDialog
            .confirm({ focusOnOpen: false })
            .title('')
            .textContent(msg)
            .targetEvent(ev)
            .ok('Sim')
            .cancel('Não');
        }

        vm.isImovelUniaoParcialmente = function () {
          return (
            vm.imovel &&
            vm.imovel.proprietario &&
            vm.imovel.proprietario.tipoProprietarioReal &&
            vm.imovel.proprietario.tipoProprietarioReal.id ===
            PROPRIETARIO_REAL_UNIAO &&
            vm.imovel.proprietario.isIntegralmente == 'false'
          );
        };

        vm.isImovelUnidadeAutonoma = function () {
          return (
            vm.imovel &&
            vm.imovel.tipoImovel &&
            vm.imovel.tipoImovel.tipoPai === 'UNIDADE AUTÔNOMA'
          );
        };

        vm.isTipoIncorporacaoFusao = function () {
          return (
            vm.imovel.incorporacao.tipoIncorporacao &&
            vm.imovel.incorporacao.tipoIncorporacao.id === POR_FUSAO
          );
        };

        vm.limparDadosDescricaoCadastral = function () {
          vm.terreno.memorialDescritivoCadastral = undefined;
          vm.terreno.areaTotalCadastral = undefined;
          vm.terreno.areaUniaoCadastral = undefined;
          vm.terreno.fracaoIdealUniaoCadastral = undefined;
          vm.terreno.geometriaTerreno = undefined;
          vm.terreno.arquivoCadastral = {};
          vm.terreno.areaShapeFileCadastral = undefined;

          if (vm.unidadeAutonoma) {
            vm.unidadeAutonoma.fracaoIdealCadastral = undefined;
          }

          NotificadorService.notify(
            $scope,
            featureIds.TERRENO_CADASTRAL,
            notifiers.LIMPAR_DESENHO
          );
        };

        vm.salvarDadosDescricaoCadastral = function (data) {
          vm.terreno.arquivoCadastral.nome = data.arquivo.nome;
          vm.terreno.arquivoCadastral.arquivo = data.arquivo.arquivo;
          vm.terreno.arquivoCadastral.dataPlanta = data.arquivo.dataPlanta;
          vm.terreno.arquivoCadastral.escala = data.arquivo.escala;
          vm.terreno.arquivoCadastral.tipoFormaLevantamento =
            data.arquivo.tipoFormaLevantamento;
          vm.terreno.arquivoCadastral.resolucaoEspacial =
            data.arquivo.resolucaoEspacial;
          vm.terreno.arquivoCadastral.tipoEquipamento =
            data.arquivo.tipoEquipamento;

          vm.terreno.memorialDescritivoCadastral = data.memorialDescritivo;
          if (!vm.isTipoIncorporacaoFusao()) {
            vm.terreno.areaTotalCadastral = data.areaTotal;
          }
          vm.terreno.areaShapeFileCadastral = data.areaTotal;
          vm.terreno.geometriaTerreno = data.geometria;

          NotificadorService.notify(
            $scope,
            {
              feature: data.geometria,
              tipoTerreno: featureIds.TERRENO_CADASTRAL,
            },
            notifiers.UPLOAD_SHAPEFILE
          );
        };

        vm.alteraMha = function () {

          if (vm.imovel.terreno.unidadeMedidaCadastral == 'ha') {
                vm.imovel.terreno.areaTotalCadastral = vm.imovel.terreno.areaTotalCadastral / 100;
                vm.imovel.terreno.areaUniaoCadastral = vm.imovel.terreno.areaUniaoCadastral / 100;
          } else if (vm.imovel.terreno.unidadeMedidaCadastral == 'm2') {
                vm.imovel.terreno.areaTotalCadastral = vm.imovel.terreno.areaTotalCadastral * 100;
                vm.imovel.terreno.areaUniaoCadastral = vm.imovel.terreno.areaUniaoCadastral * 100;
          }

        };


        vm.validarAreaTotal = function () {
          if (
            vm.imovel.incorporacao.tipoIncorporacao.id === POR_FRACIONAMENTO &&
            vm.imovel.terreno.areaTotalCadastral &&
            !vm.imovel.terreno.geometriaTerrenoCartorial
          ) {
            ValidaAreaTerrenoService.get({
              ripOrigem: vm.imovel.incorporacao.codigoOrigem,
              area: vm.imovel.terreno.areaTotalCadastral,
            }).$promise.then(function (response) {
              if (!response.isValid) {
                vm.imovel.terreno.areaTotalCadastral = undefined;
                MensagensService.exibirMensagemErro('Área total inválida com a área do imóvel original');
              }
            });
          }
        };

        vm.validarAreaTotalCadastral = function () {

            if (
              vm.imovel.terreno.areaTotalCadastral &&
              vm.imovel.terreno.areaTotalCartorial > 0
            ) {
              if (
                vm.imovel.terreno.areaTotalCadastral !==
                vm.imovel.terreno.areaTotalCartorial
              ) {
                vm.imovel.terreno.areaTotalCadastral = undefined;
                MensagensService.exibirMensagemErro(
                  'A Área Cadastral e Cartorial são diferentes.'
                );
              }
            }
          };

        vm.validarAreaUniao = function () {

          if (vm.isImovelUnidadeAutonoma() && vm.terreno.areaTotalCadastral != null && vm.terreno.areaUniaoCadastral==null){
            vm.terreno.areaUniaoCadastral = vm.terreno.areaTotalCadastral;
          }

          if (
            vm.imovel.terreno.areaUniaoCadastral &&
            vm.imovel.terreno.areaTotalCadastral
          ) {
            if (
              vm.imovel.terreno.areaTotalCadastral <
              vm.imovel.terreno.areaUniaoCadastral
            ) {
              vm.imovel.terreno.areaUniaoCadastral = undefined;
              MensagensService.exibirMensagemErro(
                'A Área da União não pode ser maior que a Área Total.'
              );
            }
          }
        };

        vm.validarFracaoIdealImovel = function () {
          if (
            vm.imovel.unidadeAutonoma &&
            vm.imovel.unidadeAutonoma.fracaoIdealCadastral === 0
          ) {
            vm.imovel.unidadeAutonoma.fracaoIdealCadastral = undefined;
            MensagensService.exibirMensagemErro(
              'A Fração Ideal do Imóvel deve ser maior que 0.'
            );
          } else {
            if (
              vm.imovel.unidadeAutonoma &&
              vm.imovel.unidadeAutonoma.fracaoIdealCadastral &&
              vm.imovel.terreno.areaTotalCadastral
            ) {
              if (
                vm.imovel.terreno.areaTotalCadastral <
                vm.imovel.unidadeAutonoma.fracaoIdealCadastral
              ) {
                vm.imovel.unidadeAutonoma.fracaoIdealCadastral = undefined;
                MensagensService.exibirMensagemErro(
                  'A Fração Ideal do Imóvel deve ser inferior à Área Total.'
                );
              } else if (
                vm.isImovelUnidadeAutonoma() &&
                vm.isImovelUniaoParcialmente()
              ) {
                if (
                  vm.imovel.unidadeAutonoma.fracaoIdealCadastral >=
                  vm.imovel.terreno.areaUniaoCadastral
                ) {
                  vm.imovel.unidadeAutonoma.fracaoIdealCadastral = undefined;
                  MensagensService.exibirMensagemErro(
                    'A Fração Ideal do Imóvel deve ser inferior à Área da União.'
                  );
                }
              }
            }

            if (
              vm.imovel.unidadeAutonoma &&
              vm.unidadeAutonoma.fracaoIdealCadastral &&
              vm.terreno.id
            ) {
              ValidaFracaoIdealTerrenoService.get({
                idTerreno: vm.terreno.id,
                ripImovel: vm.imovel.rip,
                area: vm.unidadeAutonoma.fracaoIdealCadastral,
              }).$promise.then(function (response) {
                if (!response.isValid) {
                  vm.unidadeAutonoma.fracaoIdealCadastral = undefined;
                  MensagensService.exibirMensagemErro(
                    'A soma das frações ideias das unidades autônomas desse terreno é superior a área do terreno. Favor informar outro valor de Fração Ideal do Imóvel.'
                  );
                }
              });
            }
          }
        };

        vm.formatarNumero = function (fracaoIdeal) {
          vm.imovel.unidadeAutonoma.fracaoIdealCadastral = fracaoIdeal.replace(
            /[^\d]+/g,
            ''
          );
          var stringNumero = fracaoIdeal.replace(/[^\d]+/g, '');
          var inteiro = stringNumero.slice(0, 1);
          var decimal = stringNumero.slice(1, stringNumero.length);
          var numero = 0;
          var decimalSeparator = '.';
          var stringlSeparator = ',';

          if (stringNumero.length > 1) {
            numero = inteiro + decimalSeparator + decimal;
          } else {
            numero = stringNumero;
          }

          if (numero > 1) {
            vm.imovel.unidadeAutonoma.fracaoIdealCadastral = undefined;
          } else if (numero == 1 && numero.length == 1) {
            var float = parseFloat(numero);
            vm.imovel.unidadeAutonoma.fracaoIdealCadastral = $filter('number')(
              float,
              9
            );
            $scope.fracaoIdealCadastralNum = float.toFixed(9);
            $rootScope.fracaoIdealCadastralNum = $scope.fracaoIdealCadastralNum;
          } else if (numero[0] == 0 && numero.length > 1) {
            vm.imovel.unidadeAutonoma.fracaoIdealCadastral =
              inteiro + stringlSeparator + decimal;
            $scope.fracaoIdealCadastralNum = parseFloat(
              inteiro + decimalSeparator + decimal
            );
            $rootScope.fracaoIdealCadastralNum = $scope.fracaoIdealCadastralNum;
          }
        };

        vm.desabilitarMemorialDescritivo = function () {
          return (
            !vm.imovel.terreno.geometriaTerreno /* ||
            ((isFormaLevantamentoImagensSensoriais() ||
              isFormaLevantamentoAerofotogametria()) &&
              vm.imovel.terreno.arquivoCadastral.resolucaoEspacial <= 0.3) ||
            (isFormaLevantamentoTopografico() &&
              !isTipoEquipamentoGPSNavegacao()) ||
            isTipoFormaLevantamentoGoogle()
          */);
        };

        function isFormaLevantamentoImagensSensoriais() {
          if (
            vm.imovel.terreno.arquivoCadastral &&
            vm.imovel.terreno.arquivoCadastral.tipoFormaLevantamento
          ) {
            return (
              vm.imovel.terreno.arquivoCadastral.tipoFormaLevantamento.id === 1
            );
          }
          return false;
        }

        function isFormaLevantamentoAerofotogametria() {
          if (
            vm.imovel.terreno.arquivoCadastral &&
            vm.imovel.terreno.arquivoCadastral.tipoFormaLevantamento
          ) {
            return (
              vm.imovel.terreno.arquivoCadastral.tipoFormaLevantamento.id === 2
            );
          }
          return false;
        }

        function isFormaLevantamentoTopografico() {
          if (
            vm.imovel.terreno.arquivoCadastral &&
            vm.imovel.terreno.arquivoCadastral.tipoFormaLevantamento
          ) {
            return (
              vm.imovel.terreno.arquivoCadastral.tipoFormaLevantamento.id === 3
            );
          }
          return false;
        }

        function isTipoEquipamentoGPSNavegacao() {
          if (
            vm.imovel.terreno.arquivoCadastral &&
            vm.imovel.terreno.arquivoCadastral.tipoEquipamento
          ) {
            return vm.imovel.terreno.arquivoCadastral.tipoEquipamento.id === 2;
          }
          return false;
        }
        function isTipoFormaLevantamentoGoogle() {
          if (
            vm.imovel.terreno.arquivoCadastral &&
            vm.imovel.terreno.arquivoCadastral.tipoFormaLevantamento
          ) {
            return (
              vm.imovel.terreno.arquivoCadastral.tipoFormaLevantamento.id === 4
            );
          }
          return false;
        }
        vm.isDetalhar = function () {
          return vm.action === 'detail';
        };
        vm.isAlterar = function () {
          return vm.action === 'edit';
        };
        vm.isSpiunet = function () {
          return !!vm.imovel.dsSpiunet
        };

        var unidade;
        vm.calculaFracaoAreaTotal = function () {
          var fracaoNum;
          if (vm.imovel.terreno.areaTotalCadastral && vm.imovel.fracaoIdeal) {
            if (vm.imovel.terreno.unidadeMedidaCadastral === 'm2') {
              unidade = 'm²: ';
            } else {
              unidade = 'ha: ';
            }
            vm.imovel.fracaoIdeal = String(vm.imovel.fracaoIdeal);
            fracaoNum = vm.imovel.fracaoIdeal.replace(',', '.');
            vm.imovel.fracaoAreaTotal =
              vm.imovel.terreno.areaTotalCadastral * fracaoNum;
            vm.imovel.fracaoAreaTotal = String(
              vm.imovel.fracaoAreaTotal.toFixed(2)
            );
            vm.imovel.fracaoAreaTotal =
              unidade + vm.imovel.fracaoAreaTotal.replace('.', ',');
          }
        };

        vm.calculaFracaoAreaUniao = function () {
          var fracaoNum;

          if (vm.imovel.terreno.areaUniaoCadastral && vm.imovel.fracaoIdeal) {
            if (vm.imovel.terreno.unidadeMedidaCadastral === 'm2') {
              unidade = 'm²: ';
            } else {
              unidade = 'ha: ';
            }
            vm.imovel.fracaoIdeal = String(vm.imovel.fracaoIdeal);
            fracaoNum = vm.imovel.fracaoIdeal.replace(',', '.');
            vm.imovel.fracaoAreaUniao =
              vm.imovel.terreno.areaUniaoCadastral * fracaoNum;
            vm.imovel.fracaoAreaUniao = String(
              vm.imovel.fracaoAreaUniao.toFixed(2)
            );
            vm.imovel.fracaoAreaUniao =
              unidade + vm.imovel.fracaoAreaUniao.replace('.', ',');
          }
        };

        vm.exibirMsgConfirmacaoEdicao = function(){
          if (vm.isImovelUnidadeAutonoma() && !vm.jaConfirmou && vm.imovel.id !=null && !vm.isDetalhar()){
            var confirm = $mdDialog.alert({
              focusOnOpen: false,
              textContent: "A edição deste campo atualizará todos os RIPs de unidade autônoma no mesmo terreno. Todos os campos dessa seção serão atualizados.",
              ok: "Ok"
            });
            vm.jaConfirmou = true;
            $mdDialog.show(confirm).then(function() {

            }, function() {

            });
          }
        }

        NotificadorService.subscribe(vm, function(evento, escopo, tabIndex) {
          if (tabIndex == ABA_TERRENO && (vm.terreno.id == null || vm.terreno.id == undefined)){
            vm.terreno = {arquivoCadastral:{}};
          } else if (tabIndex == ABA_TERRENO){
            //Limpa só restrição de uso
            vm.terreno.isAreaInteresseSocial = undefined;
            vm.terreno.isAreaSeguranca = undefined;
            vm.terreno.isAreaNonAedificandi = undefined;
            vm.terreno.isPatrimonioTombado = undefined;
            vm.terreno.descricaoPatrimonioTombado = "";
            vm.terreno.isOutraRestricao = undefined;
            vm.terreno.descricaoOutraRestricao = "";

          } else if (tabIndex == ABA_LOCALIZACAO){
            vm.terreno = {arquivoCadastral:{}};
          }
        }, notifiers.LIMPAR_ABA);


      }
    };
  }
})();

(function () {
    "use strict";
    directive.$inject = ["EmpresaFornecedoraService", "$mdDialog", "MensagensService", "PaginacaoFactory"];
    angular.module("su-cadastro-imovel").directive("dadosMedidor", directive);

    function directive(EmpresaFornecedoraService, $mdDialog, MensagensService, PaginacaoFactory) {
        return {
            restrict: "E",
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/dados-medidor/templates/dados-medidor.html",
            scope: {
                model: "=?",
                uf: "=",
                action: "@",
                desabilitar: "="
            },
            link: function ($scope) {

                var vm = $scope;
                vm.querySearch = querySearch;
                vm.selectedItem = null;
                vm.searchText = null;
                vm.dadosMedidor = {};
                vm.simulateQuery = false;
                vm.isDisabled  = false;
                vm.ordenacao = null;
                vm.paginacao = PaginacaoFactory.getDefault();

                vm.tiposFornecedores = [{
                    name:"TIPO_ENERGIA",
                    tipoFornecedora: "Medidor de Luz"
                },{
                    name:"TIPO_AGUA",
                    tipoFornecedora: "Medidor de Água/Esgoto"
                }];

                vm.validarDadosMedidor = function () {
                    if(vm.dadosMedidor.tipoFornecedor && vm.dadosMedidor.codigo && isEmpresaFornecedoraPreenchido()){
                        vm.adicionarDadosMedidor(vm.dadosMedidor);
                    }else{
                        $scope.forms.dadosMedidorForm.$setSubmitted();
                        MensagensService.exibirMensagemCamposObrigatorios();
                    }
                };

                vm.adicionarDadosMedidor = function (dadosmedidor) {
                    vm.dadosMedidorAux = dadosmedidor;
                    vm.dadosMedidorAux.empresaFornecedora = vm.dadosMedidor.empresaFornecedora;
                    vm.dadosMedidorAux.empresaFornecedora.tipoFornecedoraEnum = vm.dadosMedidor.tipoFornecedor;
                    vm.dadosMedidorAux.codigo = vm.dadosMedidor.codigo;

                    if(vm.isNovoMedidor(dadosmedidor)) {
                        vm.model.push(vm.dadosMedidorAux);
                    }else{
                        vm.model.splice(dadosmedidor.index, 1, vm.dadosMedidorAux);
                    }
                        vm.limparDadosMedidor();
                    };

                vm.editarDadosMedidor = function (ev, dadosMedidor) {
                    vm.dadosMedidor.empresaFornecedora = dadosMedidor.empresaFornecedora;
                    vm.dadosMedidor.tipoFornecedor = dadosMedidor.empresaFornecedora.tipoFornecedoraEnum;
                    vm.dadosMedidor.codigo = dadosMedidor.codigo;
                    vm.dadosMedidor.id = dadosMedidor.id;
                    vm.dadosMedidor.index = vm.model.indexOf(dadosMedidor);
                };

                vm.excluirDadosMedidor = function (ev, dadosMedidor) {
                    var confirm = $mdDialog.confirm({
                        focusOnOpen: false
                    })
                        .textContent("Deseja remover este medidor?")
                        .targetEvent(ev)
                        .cancel("Não")
                        .ok("Sim");

                    $mdDialog.show(confirm).then(
                        function () {
                            var index = vm.model.indexOf(dadosMedidor);
                            vm.model.splice(index, 1);
                        }
                    );
                };

                vm.limparDadosMedidor = function () {
                    vm.dadosMedidor = {};
                    vm.searchText = null;
                    vm.forms.dadosMedidorForm.$setUntouched();
                    vm.forms.dadosMedidorForm.$setPristine();
                };

                vm.pesquisaEmpresa = function() {
                    vm.empresasFornecedoras = [];
                    vm.dadosMedidor.empresaFornecedora = null;
                    if(vm.dadosMedidor.tipoFornecedor.name === "TIPO_ENERGIA") {
                        EmpresaFornecedoraService.consultarEmpresaTipoEnergia.query({tipoFornecedor: "TIPO_ENERGIA", uf: vm.uf}, function (result) {
                            vm.empresasFornecedoras = result;
                            vm.empresasFornecedoras = vm.loadAll();
                        });
                    }else {
                        EmpresaFornecedoraService.consultarEmpresaTipoAgua.query({tipoFornecedor: "TIPO_AGUA", uf: vm.uf}, function (result) {
                            vm.empresasFornecedoras = result;
                            vm.empresasFornecedoras = vm.loadAll();
                        });
                    }
                };

                function querySearch(query) {
                    var results = query ? vm.empresasFornecedoras.filter( vm.createFilterFor(query) ) : vm.empresasFornecedoras;
                        return results;
                }

                vm.loadAll = function () {
                    return vm.empresasFornecedoras.map( function (empresa) {
                        return {
                            value: empresa.nome.toLowerCase(),
                            nome: empresa.nome,
                            tipoFornecedor: empresa.tipoFornecedoraEnum,
                            uf: empresa.uf,
                            id: empresa.id
                        };
                    });
                };

                vm.createFilterFor = function (query) {
                    var lowercaseQuery = angular.lowercase(query);

                    return function filterFn(empresa) {
                        return (empresa.value.indexOf(lowercaseQuery) >= 0);
                    }
                };

                vm.isNovoMedidor = function (dadosMedidor) {
                    return angular.isUndefined(dadosMedidor.index) || dadosMedidor.index == null;
                };

                function isEmpresaFornecedoraPreenchido() {
                    if (vm.dadosMedidor.empresaFornecedora) {
                        return true;
                    } else if (vm.dadosMedidor.tipoFornecedor.name === "TIPO_AGUA" && vm.searchText) {
                        vm.dadosMedidor.empresaFornecedora = {
                            nome: vm.searchText,
                            uf: vm.uf,
                            tipoFornecedoraEnum: vm.dadosMedidor.tipoFornecedor
                        };
                        return true;
                    } else {
                        return false;
                    }
                }

                vm.dadosMedidorDisabled = function () {
                  return isDetalhar();
                };

                function isDetalhar(){
                    return vm.action === 'detail' || vm.desabilitar  ;
                }
            }
        }
    }
})();

(function () {
  'use strict';
  angular
    .module('su-cadastro-imovel')
    .controller('SelectFundamentoController', ["$scope", "$element", function ($scope, $element) {
      $scope.searchFundamento;
      $scope.clearSearchFundamento = function () {
        $scope.searchFundamento = '';
      };

      // The md-select directive eats keydown events for some quick select
      // logic. Since we have a search input here, we don't need that logic.
      /* $element.find('#inputFundamento').on('keydown', function (ev) {
        console.log('executando ev.stopPropagation()');
        ev.stopPropagation();
      });*/
    }]);
})();

(function () {
  'use strict';
  detalhesImovel.$inject = ["NaturezaImovelService", "SistemaService", "SincronizarService", "$window", "$timeout"];
  angular
    .module('su-cadastro-imovel')
    .directive('secaoDetalhesImovel', detalhesImovel);

  function detalhesImovel(
    NaturezaImovelService,
    SistemaService,
    SincronizarService,
    $window,
    $timeout
  ) {
    return {
      templateUrl:
        'scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/templates/detalhes-imovel.html',
      scope: {
        imovel: '=',
        isDetail: '=',
        desabilitar: "="
      },
      link: function ($scope) {
        var vm = $scope,
          ID_NATUREZA_IMOVEL_URBANO = 1,
          ID_NATUREZA_IMOVEL_RURAL = 2;

        vm.tiposNaturezaImovel = NaturezaImovelService.query();
        vm.sistema = SistemaService.query();

        $scope.$watch('imovelSpiunet', function () {
          if (vm.imovelSpiunet) {
            vm.imovelLegado = false;
            vm.imovel.dsNbp = undefined;
            vm.imovel.dsSiapa = undefined;

            if (!vm.imovel.dsSpiunet) {
              vm.imovel.dsSpiunet = 'pend. sincronizacao';
            }
          } else {
            if (!vm.isDetalhar() && !vm.isAlteracaoImovel()) {
              vm.imovel.dsSpiunet = undefined;
            }
          }
          vm.isOriundo();
        });

        $scope.$watch('imovelLegado', function () {
          if (vm.imovelLegado) {
            vm.ripMigradoSpiuNet = vm.imovel.dsSpiunet;
            vm.imovelSpiunet = false;
            vm.imovel.dsSpiunet = undefined;
          } else {
            vm.imovel.dsNbp = undefined;
            vm.imovel.dsSiapa = undefined;
          }
          vm.isOriundo();
        });

        vm.isOriundo = function () {
          vm.imovel.imovelOriundo = vm.imovelSpiunet || vm.imovelLegado;
          return vm.imovel.imovelOriundo;
        };

        // Opção desabilitada devido à migração de imóveis SPIUnet para a base do SPUnet
        // vm.isSistemaSPIUnet = function () {
        //     if (vm.imovel.sistema && vm.imovel.sistema.length > 0) {
        //         for (var i = 0; i < vm.imovel.sistema.length; i++) {
        //             if (vm.imovel.sistema[i].id === 1) {
        //                 return true
        //             } else if (i === vm.imovel.sistema.length-1){
        //                 vm.imovel.dsSpiunet = undefined;
        //                 return false
        //             }
        //         }
        //     }
        // };

        vm.isSistemaSIAPA = function () {
          if (vm.imovel.sistema && vm.imovel.sistema.length > 0) {
            for (var i = 0; i < vm.imovel.sistema.length; i++) {
              if (vm.imovel.sistema[i].id === 2) {
                return true;
              } else if (i === vm.imovel.sistema.length - 1) {
                vm.imovel.dsSiapa = undefined;
                return false;
              }
            }
          }
        };

        vm.isImovelSpiunet = function () {
          if (vm.imovel.migracaoSpiunet) {
            return false;
          } else if (vm.imovel.sistema && vm.imovel.sistema.length > 0) {
            for (var i = 0; i < vm.imovel.sistema.length; i++) {
              if (vm.imovel.sistema[i].descricao === 'SPIUnet') {
                return true;
              } else {
                return false;
              }
            }
          }
        };

        vm.isImovelLegadoSpiuNet = function () {
          if (vm.imovel.migracaoSpiunet) {
            return true;
          }
          return false;
        };

        vm.isImovelSincronizado = function () {
          if (vm.imovel.dsSpiunet) {
            return true;
          }
          return false;
        };

        vm.isSistemaCIDISARP = function () {
          if (vm.imovel.sistema && vm.imovel.sistema.length > 0) {
            for (var i = 0; i < vm.imovel.sistema.length; i++) {
              if (vm.imovel.sistema[i].id === 3) {
                return true;
              } else if (i === vm.imovel.sistema.length - 1) {
                vm.imovel.dsNbp = undefined;
                return false;
              }
            }
          }
        };

        vm.isNaturezaImovelUrbano = function () {
          if (vm.imovel.numeroInscricaoMunicipal != null) {
            vm.imovel.numeroInscricaoMunicipal = String(
              vm.imovel.numeroInscricaoMunicipal
            );
          }
          return (
            vm.imovel.naturezaImovel &&
            vm.imovel.naturezaImovel.id === ID_NATUREZA_IMOVEL_URBANO
          );
        };

        vm.isNaturezaImovelRural = function () {
          if (vm.imovel.numeroInscricaoRural != null) {
            vm.imovel.numeroInscricaoRural = String(
              vm.imovel.numeroInscricaoRural
            );
          }
          return (
            vm.imovel.naturezaImovel &&
            vm.imovel.naturezaImovel.id === ID_NATUREZA_IMOVEL_RURAL
          );
        };

        vm.isEspelhoDagua = function () {
          return vm.imovel && vm.imovel.tipoImovel && vm.imovel.tipoImovel.id === 3;
        };

        vm.isDetalhar = function () {
          return vm.isDetail;
        };

        vm.isAlteracaoImovel = function () {
          return vm.imovel.id != null;
        };
      }
    };
  }
})();

(function () {
  'use strict';
  dadosIncorporacao.$inject = ["TipoIncorporacaoService", "SituacaoIncorporacaoService", "FundamentoService", "TipoAquisicaoService", "TipoFracionamentoService", "ValidaRipFracionamentoService", "NotificadorService", "NOTIFICADORES", "ValidaRipFusaoService", "$mdDialog", "TipoContratoPessoalService", "DireitoIncorporadoService", "PesquisarImovelMatricula", "ExibirCertidao", "tabsIncorporacao", "MensagensService", "$state", "PaginacaoFactory", "$window", "$document", "IntegracaoSeiService", "ABA_DADOS_GERAIS"];
  angular
    .module('su-cadastro-imovel')
    .directive('secaoDadosIncorporacao', dadosIncorporacao);

  function isFundamentoPermitido(f) {
    /* eslint-disable no-var */
    var idsFundamentosPermitidos = { 1: true, 2: true, 3: true, 4: true, 14: true };
    /* eslint-enable no-var */
    return idsFundamentosPermitidos[f.id];
  }

  function dadosIncorporacao(
    TipoIncorporacaoService,
    SituacaoIncorporacaoService,
    FundamentoService,
    TipoAquisicaoService,
    TipoFracionamentoService,
    ValidaRipFracionamentoService,
    NotificadorService,
    NOTIFICADORES,
    ValidaRipFusaoService,
    $mdDialog,
    TipoContratoPessoalService,
    DireitoIncorporadoService,
    PesquisarImovelMatricula,
    ExibirCertidao,
    tabsIncorporacao,
    MensagensService,
    $state,
    PaginacaoFactory,
    $window,
    $document,
    IntegracaoSeiService,
    ABA_DADOS_GERAIS
  ) {
    return {
      templateUrl:
        'scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/templates/dados-incorporacao.html',
      scope: {
        imovel: '=',
        tituloBackup: '=',
        titulo: '=',
        queryPagination: '=',
        isExibeTabTituloAquisitivo: '=',
        action: '=',
        desabilitar: "="
      },
      controller: ["$rootScope", "$scope", function ($rootScope, $scope) {
        var vm = this,
          scope = $scope;
        vm.usuarioLogado = $rootScope.usuarioLogado || { permissoes: [] };
        vm.blockNumProcesso = false;
        vm.hoje = new Date();
        vm.ugProprietario = {};
        vm.ugProprietarioParcial = {};
        vm.ugProprietarioTitular = {};
        vm.orgaoEntidadeProprietario = {};
        vm.orgaoEntidadeTitular = {};
        vm.orgaoEntidadeAnterior = {};
        vm.ugProprietarioAnterior = {};
        vm.orgaoEntidadeProprietarioParcial = {};
        vm.ugsTitular = {};
        vm.ugsAnterior = {};
        vm.isExibeTabTituloAquisitivo = scope.isExibeTabTituloAquisitivo;
        vm.tituloBackup = scope.tituloBackup;
        vm.titulo = scope.titulo;
        vm.queryPagination = scope.queryPagination;
        vm.titulos = scope.imovel.titulos;
        vm.action = scope.action;
        vm.showCessaoOnerosa = false;
        vm.showDireitoSuperficieOneroso = false;
        vm.showCdruOnerosa = false;
        vm.hoje = new Date();

        vm.exibeTab = function (isExibeTabTituloAquisitivo, tituloAquisitivo) {
          if (tituloAquisitivo) {
            scope.titulo = angular.copy(tituloAquisitivo);
            scope.tituloBackup = tituloAquisitivo;
          }
          scope.isExibeTabTituloAquisitivo = isExibeTabTituloAquisitivo;
        };
      }],
      controllerAs: 'ctrlDadosIncorporacao',
      link: function ($scope, element, attrs, ctrlDadosIncorporacao) {
        var vm = $scope,
          init,
          ID_TIPO_AQUISICAO_COMPRA = 2,
          ID_TIPO_AQUISICAO_RECEBIMENTO_EM_DOACAO = 8,
          ID_TIPO_IMOVEL_ESPELHO_DAGUA = 3,
          ID_TIPO_IMOVEL_CAVIDADE = 5,
          ID_TIPO_IMOVEL_GLEBA_TERRENO_LOTE_SEM_ED = 1,
          ID_TIPO_IMOVEL_GLEBA_TERRENO_LOTE_COM_ED = 2,
          TIPO_IMOVEL_UNIDADE_AUTONOMA = 'UNIDADE AUTÔNOMA',
          ID_FORMA_INC_ORIGINALMENTE_DA_UNIAO = 1,
          ID_FORMA_INC_AQUISICAO = 2,
          ID_FORMA_INC_FRACIONAMENTO = 3,
          ID_FORMA_INC_FUSAO = 4,
          ID_FORMA_INC_IMOVEIS_TERCEIROS = 5,
          ID_SITUACAO_INCORPORACAO_INCORPORADO = 1,
          ID_SITUACAO_INCORPORACAO_NAO_SE_APLICA = 3,
          PERMISSAO_CADASTRAR_IMOVEL_UNIAO =
            'CAD_IMOVEL.CADASTRAR_IMOVEL_UNIAO',
          ID_TIPO_CONTRATO_PESSOAL_OUTRO = 6,
          TAMANHO_RIP_VALIDO = 8,
          limparDadosRip,
          excluiBenfeitoriasRipExcluido;

        vm.ordenacaoTabelaUnificacao = null;
        vm.paginacaoUnificacao = PaginacaoFactory.getDefault();
        vm.tiposFormaIncorporacao = [];

        init = function () {
          vm.buscaProcessoSEI();
          vm.imovel.ripsFusao = vm.imovel.ripsFusao || [];
          vm.isRipFracionamentoValido = false;
          vm.situacoesIncorporacao = SituacaoIncorporacaoService.query();
          vm.fundamentos = FundamentoService.query();
          vm.tiposAquisicao = TipoAquisicaoService.query();
          vm.tiposFracionamento = TipoFracionamentoService.query();
          vm.tiposContratoPessoal = TipoContratoPessoalService.query();
          vm.direitosIncorporados = DireitoIncorporadoService.query();
          vm.hoje = new Date();
          vm.imovel.linkProcessoSei = null;
          vm.exibeMengSei = false;
          vm.blockNumProcesso = false;
          TipoIncorporacaoService.query().$promise.then(function (
            tiposIncorp,
            index
          ) {
            if (
              !ctrlDadosIncorporacao.usuarioLogado.permissoes.includes(
                PERMISSAO_CADASTRAR_IMOVEL_UNIAO
              )
            ) {
              angular.forEach(tiposIncorp, function (tipo) {
                if (tipo.id === ID_FORMA_INC_ORIGINALMENTE_DA_UNIAO) {
                  tiposIncorp.splice(index, 1);
                }
              });
            }
            vm.tiposFormaIncorporacao = tiposIncorp;
          });
          vm.handleTipoIncorporacaoContratoPessoal();

          if (vm.imovel.numeroProcesso) {
            vm.imovel.numeroProcesso = vm.imovel.numeroProcesso.replace(/(\d{5})(\d{6})(\d{4})(\d{2})/,"$1.$2/$3-$4");
          }
        };

        vm.$watchCollection('imovel.ripsFusao', function (rips) {
          var hasBenfeitoria = false;
          var benfeitorias = [];
          var areaTotal = 0;
          angular.forEach(rips, function (rip, index) {
            var areaTotalTerreno =
              rip.terreno.areaTotalCartorial > 0
                ? rip.terreno.areaTotalCartorial
                : rip.terreno.areaTotalCadastral;
            areaTotal = areaTotal + areaTotalTerreno;
            if (rip.benfeitorias && rip.benfeitorias.length > 0) {
              tabsIncorporacao.atualizaTabs(rip.tipoImovel);
              hasBenfeitoria = true;
              angular.forEach(rip.benfeitorias, function (b) {
                b.codigo = gerarCodigoBenfeitoria(b);
                benfeitorias.push(b);
              });
            }

            if (index === rips.length - 1 && !hasBenfeitoria) {
              tabsIncorporacao.atualizaTabs(vm.imovel.tipoImovel);
              reordenarCodigoBenfeitorias();
            }
          });

          if (rips.length === 0) {
            tabsIncorporacao.atualizaTabs(vm.imovel.tipoImovel);
            reordenarCodigoBenfeitorias();
          }

          vm.imovel.terreno.areaTotalCadastral =
            areaTotal > 0 ? areaTotal : vm.imovel.terreno.areaTotalCadastral;
          vm.imovel.benfeitorias = mergeArraySemDuplicacoes(
            vm.imovel.benfeitorias.concat(benfeitorias)
          );
          ordenaBenfeitorias();
        });

        function ordenaBenfeitorias() {
          vm.imovel.benfeitorias.sort(function (a, b) {
            if (a.codigo < b.codigo) {
              return -1;
            }
            if (a.codigo > b.codigo) {
              return 1;
            }
            return 0;
          });
        }

        function mergeArraySemDuplicacoes(array) {
          var a = array.concat();
          for (var i = 0; i < a.length; ++i) {
            for (var j = i + 1; j < a.length; ++j) {
              if (a[i] === a[j]) a.splice(j--, 1);
            }
          }
          return a;
        }

        function reordenarCodigoBenfeitorias() {
         /* angular.forEach(vm.imovel.benfeitorias, function (benfeitoria) {
            benfeitoria.codigo = gerarCodigoBenfeitoria(benfeitoria);
            });*/
        }

        function gerarCodigoBenfeitoria(benfeitoria) {
          var prefixo = vm.isBenfeitoriaEdificacao(benfeitoria) ? 'E' : 'C';
          var arrayCtrl = [];
          var count = 1;
          for (var i = 0; i < vm.imovel.benfeitorias.length; i++) {
            if (vm.imovel.benfeitorias[i].codigo.indexOf(prefixo) === 0) {
              arrayCtrl.push(
                parseInt(vm.imovel.benfeitorias[i].codigo.substr(1))
              );
            }
          }
          while (arrayCtrl.includes(count)) {
            count++;
          }
          return prefixo + count;
        }

        vm.isBenfeitoriaEdificacao = function (benfeitoria) {
          return (
            benfeitoria.tipoBenfeitoria && benfeitoria.tipoBenfeitoria.id === 1
          );
        };

        excluiBenfeitoriasRipExcluido = function (rip) {
          var benfeitorias = vm.imovel.benfeitorias;
          angular.forEach(benfeitorias, function (benfeitoria, index) {
            angular.forEach(rip.benfeitorias, function (b) {
              if (benfeitoria.id === b.id) {
                benfeitorias.splice(index, 1);
              }
            });
          });
        };

        if (!vm.imovel.incorporacao.isExOficio) {
          //TODO estava impedindo de trazer o campo A Pedido? marcado.
          //vm.imovel.incorporacao.isExOficio = null;
        } else {
          vm.imovel.incorporacao.isExOficio == true
            ? (vm.validaLpm = true)
            : (vm.validaLpm = false);
        }

        vm.isTipoAquisicaoCompra = function () {
          return (
            vm.imovel.incorporacao.tipoAquisicao &&
            vm.imovel.incorporacao.tipoAquisicao.id === ID_TIPO_AQUISICAO_COMPRA
          );
        };

        vm.isTipoAquisicaoRecebimentoEmDoacao = function () {
          return (
            vm.imovel.incorporacao.tipoAquisicao &&
            vm.imovel.incorporacao.tipoAquisicao.id ===
              ID_TIPO_AQUISICAO_RECEBIMENTO_EM_DOACAO
          );
        };

        vm.isTipoImovelCavidade = function () {
          return (
            vm.imovel.tipoImovel &&
            vm.imovel.tipoImovel.id === ID_TIPO_IMOVEL_CAVIDADE
          );
        };

        vm.isTipoImovelEspelhoDagua = function () {
          return (
            vm.imovel.tipoImovel &&
            vm.imovel.tipoImovel.id === ID_TIPO_IMOVEL_ESPELHO_DAGUA
          );
        };

        vm.isMatriculaObrigatoria = function () {

          var matriculaObrigatoria = true;

          switch (vm.imovel) {
          case vm.imovel.tipoImovel.id === 3:
             matriculaObrigatoria = false;
            break;
          case vm.imovel.incorporacao.tipoIncorporacao === 1:
            matriculaObrigatoria = false;
            break;
          case vm.imovel.incorporacao.situacaoIncorporacao === 2:
            matriculaObrigatoria = false;
            break;
          }

          return matriculaObrigatoria;
        };

        vm.isTipoImovelUnidadeAutonoma = function () {
          return (
            vm.imovel &&
            vm.imovel.tipoImovel &&
            vm.imovel.tipoImovel.tipoPai === TIPO_IMOVEL_UNIDADE_AUTONOMA
          );
        };

        vm.naoExibeNaoSeAplica = function (situacao) {
          return !(
            situacao.id === ID_SITUACAO_INCORPORACAO_NAO_SE_APLICA &&
            !(vm.isTipoImovelCavidade() || vm.isTipoImovelEspelhoDagua())
          );
        };

        vm.isFormaIncorporacaoOriginalmenteUniao = function () {
          return (
            vm.imovel.incorporacao.tipoIncorporacao &&
            vm.imovel.incorporacao.tipoIncorporacao.id ===
              ID_FORMA_INC_ORIGINALMENTE_DA_UNIAO
          );
        };

        vm.showSituacaoIncorporacao = function () {

          if (vm.imovel.incorporacao.tipoIncorporacao) {

            if (vm.imovel.incorporacao.tipoIncorporacao.id === ID_FORMA_INC_ORIGINALMENTE_DA_UNIAO) {
              /* eslint-disable no-var */
              var exibeCampo = false;
              /* eslint-enable no-var */

              if (vm.imovel.incorporacao.fundamento && vm.imovel.incorporacao.fundamento.length > 0) {
                exibeCampo = vm.imovel.incorporacao.fundamento.some(isFundamentoPermitido);
              }

              if (exibeCampo) {
                return true;
              }

              vm.alterarSituacaoIncorporacao(ID_SITUACAO_INCORPORACAO_INCORPORADO);
              return false;
            }
          }
          return true;
        };

        vm.alterarSituacaoIncorporacao = function(idSituacao) {

          for(var i = 0; i < vm.situacoesIncorporacao.length; i++) {
            if (vm.situacoesIncorporacao[i].id === idSituacao) {
              vm.imovel.incorporacao.situacaoIncorporacao = vm.situacoesIncorporacao[i];
            }
          }
        }

        vm.isFormaIncorporacaoAquisicao = function () {
          return (
            vm.imovel.incorporacao.tipoIncorporacao &&
            vm.imovel.incorporacao.tipoIncorporacao.id ===
              ID_FORMA_INC_AQUISICAO
          );
        };

        vm.isFormaIncorporacaoImoveisDeTerceiros = function () {
          return (
            vm.imovel.incorporacao.tipoIncorporacao &&
            vm.imovel.incorporacao.tipoIncorporacao.id ===
              ID_FORMA_INC_IMOVEIS_TERCEIROS
          );
        };

        vm.isFormaIncorporacaoFracionamento = function () {
          return (
            vm.imovel.incorporacao.tipoIncorporacao &&
            vm.imovel.incorporacao.tipoIncorporacao.id ===
              ID_FORMA_INC_FRACIONAMENTO
          );
        };

        vm.isFormaIncorporacaoFusao = function () {
          return (
            vm.imovel.incorporacao.tipoIncorporacao &&
            vm.imovel.incorporacao.tipoIncorporacao.id === ID_FORMA_INC_FUSAO
          );
        };

        vm.isDoacaoComEncargo = function () {
          return vm.imovel.incorporacao.doacaoComEncargo;
        };

        vm.isTipoImovelGlebaTerrenoLoteComEdificacao = function () {
          return (
            vm.imovel.tipoImovel &&
            vm.imovel.tipoImovel.id === ID_TIPO_IMOVEL_GLEBA_TERRENO_LOTE_COM_ED
          );
        };

        vm.isTipoImovelGlebaTerrenoLoteSemEdificacao = function () {
          return (
            vm.imovel.tipoImovel &&
            vm.imovel.tipoImovel.id === ID_TIPO_IMOVEL_GLEBA_TERRENO_LOTE_SEM_ED
          );
        };

        vm.isTipoContratoPessoalOutro = function () {
          return (
            vm.isFormaIncorporacaoImoveisDeTerceiros() &&
            vm.imovel.incorporacao.tipoContratoPessoal &&
            vm.imovel.incorporacao.tipoContratoPessoal.id ===
              ID_TIPO_CONTRATO_PESSOAL_OUTRO
          );
        };

        vm.isSituacaoIncorporacaoIncorporado = function () {
          return (
            vm.imovel.incorporacao.situacaoIncorporacao &&
            vm.imovel.incorporacao.situacaoIncorporacao.id ===
              ID_SITUACAO_INCORPORACAO_INCORPORADO
          );
        };

        vm.validaExOficio = function () {
          if (
            vm.imovel.incorporacao.fundamento &&
            vm.imovel.incorporacao.fundamento.length > 0
          ) {
            for (var i = 0; i < vm.imovel.incorporacao.fundamento.length; i++) {
              if (
                vm.imovel.incorporacao.fundamento[i].id == 1 ||
                vm.imovel.incorporacao.fundamento[i].id == 2
              ) {
                return true;
              }
            }
          } else {
            return false;
          }
        };

        vm.handleTipoIncorporacaoContratoPessoal = function () {
          if (vm.imovel.incorporacao.tipoIncorporacao) {
            vm.imovel.incorporacao.tipoIncorporacao.id === 5 &&
            vm.imovel.incorporacao.tipoContratoPessoal && vm.imovel.incorporacao.tipoContratoPessoal.id == 4
              ? (vm.showCessaoOnerosa = true)
              : (vm.showCessaoOnerosa = false);
            vm.imovel.incorporacao.tipoIncorporacao.id === 5 &&
            vm.imovel.incorporacao.tipoContratoPessoal && vm.imovel.incorporacao.tipoContratoPessoal.id == 7
              ? (vm.showDireitoSuperficieOneroso = true)
              : (vm.showDireitoSuperficieOneroso = false);
            vm.imovel.incorporacao.tipoIncorporacao.id === 5 &&
            vm.imovel.incorporacao.tipoContratoPessoal && vm.imovel.incorporacao.tipoContratoPessoal.id == 8
              ? (vm.showCdruOnerosa = true)
              : (vm.showCdruOnerosa = false);
          }

            // if (vm.imovel.incorporacao.situacaoIncorporacao) {
            //    vm.imovel.incorporacao.situacaoIncorporacao.id =
            //     vm.situacoesIncorporacao[ID_SITUACAO_INCORPORACAO_EM_PROCESSO].id;
            // }
        };

        vm.handleExOficio = function () {
          vm.imovel.incorporacao.isExOficio == true
            ? (vm.validaLpm = true)
            : (vm.validaLpm = false);
        };

        vm.tiposFormaIncorporacaoFiltrados = function() {
          var tiposFormaIncorporacaoFiltrados = []
          vm.tiposFormaIncorporacao.forEach(function(formaIncorporacao) {
            if(vm.exibeOpcoesFracionamentoAndFusaoCasoTipoImovelSejaGlebaTerrenoLote(formaIncorporacao)){
              tiposFormaIncorporacaoFiltrados.push(formaIncorporacao);
            }
          })
          return tiposFormaIncorporacaoFiltrados;
        }

        vm.exibeOpcoesFracionamentoAndFusaoCasoTipoImovelSejaGlebaTerrenoLote = function (
          formaIncorporacao
        ) {
          return !(
            (formaIncorporacao.id === ID_FORMA_INC_FRACIONAMENTO ||
              formaIncorporacao.id === ID_FORMA_INC_FUSAO) &&
            !(
              vm.isTipoImovelGlebaTerrenoLoteComEdificacao() ||
              vm.isTipoImovelGlebaTerrenoLoteSemEdificacao()
            )
          );
        };

        $scope.$watch('imovel.tipoImovel', function () {
          if (vm.isTipoImovelCavidade() || vm.isTipoImovelEspelhoDagua()) {
            for (var i = 0; i < vm.tiposFormaIncorporacao.length; i++) {
              if (
                vm.tiposFormaIncorporacao[i].id ===
                ID_FORMA_INC_ORIGINALMENTE_DA_UNIAO
              ) {
                vm.imovel.incorporacao.tipoIncorporacao =
                  vm.tiposFormaIncorporacao[i];
                break;
              }
            }

            for (var j = 0; j < vm.situacoesIncorporacao.length; j++) {
              if (
                vm.situacoesIncorporacao[j].id ===
                ID_SITUACAO_INCORPORACAO_NAO_SE_APLICA
              ) {
                vm.imovel.incorporacao.situacaoIncorporacao =
                  vm.situacoesIncorporacao[j];
                break;
              }
            }

          }

          if (vm.isTipoImovelUnidadeAutonoma() && vm.hasMatricula()) {
            var matriculas = vm.imovel.incorporacao.matriculas;
            angular.forEach(matriculas, function (matricula, index) {
              var cartorio = matricula != null ? matricula.cartorio : null;
              var idMatricula = '';
              if (cartorio != undefined && cartorio != null) {
                idMatricula = matricula.cartorio.id;
              } else {
                idMatricula = matricula !=null ? matricula.cartorioID : '';
              }
              PesquisarImovelMatricula.getImovelMatricula({
                numeroMatricula: matricula.numero,
                idCartorio: idMatricula
              }).$promise.then(function (data) {
                if (data.length > 0) {
                  matriculas.splice(index, 1);
                }
              });
            });
          }
        });

        vm.hasMatricula = function () {
          return vm.imovel.incorporacao.matriculas.length > 0;
        };

        vm.hasCertidaoCartorial = function () {
          return vm.imovel.incorporacao.certidoesCartoriais.length > 0;
        };

        vm.hasTitulo = function () {
          return vm.imovel.titulos.length > 0;
        };

        vm.hasRipFusao = function () {
          return vm.imovel.ripsFusao && vm.imovel.ripsFusao.length > 0;
        };

        $scope.$watch(
          'imovel.incorporacao.codigoOrigem',
          /**
           * @param {string} value - Novo valor para o atributo codigoOrigem.
           */
          function (value) {
            if (value) {
              if (value.length === TAMANHO_RIP_VALIDO) {
                vm.validarRip();
              }
            }
          }
        );

        vm.validaTipoFundamento = function (idImovel) {
          var i;

          vm.fundamentosExibidos = {
            idDoEspelhoDAgua: [8, 9, 10, 15],
            idDaCavidadeNatural: [6, 11],
            idDaGlebaTerrenoLoteEdificacaoUnidadeAutonoma: [
              1,
              2,
              3,
              4,
              5,
              6,
              7,
              12,
              13,
              14,
              16,
              17,
            ],
          };

          if (vm.isTipoImovelEspelhoDagua()) {
            for (
              i = 0;
              i < vm.fundamentosExibidos.idDoEspelhoDAgua.length;
              i++
            ) {
              if (idImovel == vm.fundamentosExibidos.idDoEspelhoDAgua[i]) {
                return true;
              }
            }
          }
          if (vm.isTipoImovelCavidade()) {
            for (
              i = 0;
              i < vm.fundamentosExibidos.idDaCavidadeNatural.length;
              i++
            ) {
              if (idImovel == vm.fundamentosExibidos.idDaCavidadeNatural[i]) {
                return true;
              }
            }
          }
          if (
            vm.isTipoImovelGlebaTerrenoLoteComEdificacao() ||
            vm.isTipoImovelGlebaTerrenoLoteSemEdificacao() ||
            vm.isTipoImovelUnidadeAutonoma()
          ) {
            for (
              i = 0;
              i <
              vm.fundamentosExibidos
                .idDaGlebaTerrenoLoteEdificacaoUnidadeAutonoma.length;
              i++
            ) {
              if (
                idImovel ==
                vm.fundamentosExibidos
                  .idDaGlebaTerrenoLoteEdificacaoUnidadeAutonoma[i]
              ) {
                return true;
              }
            }
          }
          return false;
        };

        vm.validarRip = function () {
          var rip = vm.imovel.incorporacao.codigoOrigem;
          if (rip && rip.length === TAMANHO_RIP_VALIDO) {
            ValidaRipFracionamentoService.get({ rip: rip }).$promise.then(
              function (response) {
                if (response.isValid) {
                  vm.isRipFracionamentoValido = true;
                  vm.idImovelOrigem = response.id;
                  if (response.imovel.proprietarioAnterior) {
                    vm.imovel.proprietarioAnterior =
                      response.imovel.proprietarioAnterior;
                    ctrlDadosIncorporacao.orgaoEntidadeAnterior.id = response
                      .imovel.proprietarioAnterior.codigoOrgaoEntidade
                      ? response.imovel.proprietarioAnterior.codigoOrgaoEntidade
                      : undefined;
                    ctrlDadosIncorporacao.orgaoEntidadeAnterior.nome = response
                      .imovel.proprietarioAnterior.orgaoEntidade
                      ? response.imovel.proprietarioAnterior.orgaoEntidade
                      : undefined;
                    ctrlDadosIncorporacao.ugProprietarioAnterior.id = response
                      .imovel.proprietarioAnterior.codigoUg
                      ? response.imovel.proprietarioAnterior.codigoUg
                      : undefined;
                    ctrlDadosIncorporacao.ugProprietarioAnterior.nome = response
                      .imovel.proprietarioAnterior.unidadeUsuario
                      ? response.imovel.proprietarioAnterior.unidadeUsuario
                      : undefined;
                  }
                  vm.imovel.proprietarioTitular =
                    response.imovel.proprietarioTitular;
                  ctrlDadosIncorporacao.orgaoEntidadeProprietarioParcial.id = ctrlDadosIncorporacao.orgaoEntidadeProprietario.id = response
                    .imovel.proprietario.codigoOrgaoEntidade
                    ? response.imovel.proprietario.codigoOrgaoEntidade
                    : undefined;
                  ctrlDadosIncorporacao.orgaoEntidadeProprietarioParcial.nome = ctrlDadosIncorporacao.orgaoEntidadeProprietario.nome = response
                    .imovel.proprietario.orgaoEntidade
                    ? response.imovel.proprietario.orgaoEntidade
                    : undefined;
                  ctrlDadosIncorporacao.ugProprietarioParcial.id = ctrlDadosIncorporacao.ugProprietario.id = response
                    .imovel.proprietario.codigoUg
                    ? response.imovel.proprietario.codigoUg
                    : undefined;
                  ctrlDadosIncorporacao.ugProprietarioParcial.nome = ctrlDadosIncorporacao.ugProprietario.nome = response
                    .imovel.proprietario.unidadeUsuario
                    ? response.imovel.proprietario.unidadeUsuario
                    : undefined;
                  ctrlDadosIncorporacao.orgaoEntidadeTitular.id = response
                    .imovel.proprietarioTitular.codigoOrgaoEntidade
                    ? response.imovel.proprietarioTitular.codigoOrgaoEntidade
                    : undefined;
                  ctrlDadosIncorporacao.orgaoEntidadeTitular.nome = response
                    .imovel.proprietarioTitular.orgaoEntidade
                    ? response.imovel.proprietarioTitular.orgaoEntidade
                    : undefined;
                  ctrlDadosIncorporacao.ugProprietarioTitular.id = response
                    .imovel.proprietarioTitular.codigoUg
                    ? response.imovel.proprietarioTitular.codigoUg
                    : undefined;
                  ctrlDadosIncorporacao.ugProprietarioTitular.nome = response
                    .imovel.proprietarioTitular.unidadeUsuario
                    ? response.imovel.proprietarioTitular.unidadeUsuario
                    : undefined;
                  vm.imovel.proprietario = response.imovel.proprietario;
                  vm.imovel.proprietario.isIntegralmente = response.imovel
                    .proprietario.isIntegralmente
                    ? 'true'
                    : 'false';
                  NotificadorService.notify(
                    $scope,
                    {
                      ugProprietario: ctrlDadosIncorporacao.ugProprietario,
                      ugParcial: ctrlDadosIncorporacao.ugProprietarioParcial,
                      ugTitular: ctrlDadosIncorporacao.ugProprietarioTitular,
                      ugAnterior: ctrlDadosIncorporacao.ugProprietarioAnterior,
                      orgaoProprietario:
                        ctrlDadosIncorporacao.orgaoEntidadeProprietario,
                      orgaoParcial:
                        ctrlDadosIncorporacao.orgaoEntidadeProprietarioParcial,
                      orgaoTitular: ctrlDadosIncorporacao.orgaoEntidadeTitular,
                      orgaoAnterior:
                        ctrlDadosIncorporacao.orgaoEntidadeAnterior,
                    },
                    NOTIFICADORES.SETAR_UGS_ORGAOS
                  );
                } else {
                  if (response.mensagem){
                    MensagensService.exibirMensagemErro(response.mensagem);
                  } else {
                    MensagensService.exibirMensagemErro('RIP de Origem não está apto para essa operação');
                  }
                  limparDadosRip();
                }
              }
            );
          } else {
            limparDadosRip();
          }
        };

        limparDadosRip = function () {
          vm.idImovelOrigem = undefined;
          vm.imovel.incorporacao.codigoOrigem = undefined;
          vm.imovel.proprietarioAnterior = undefined;
          vm.imovel.proprietario = undefined;
          vm.imovel.proprietarioTitular = undefined;
          NotificadorService.notify(
            $scope,
            null,
            NOTIFICADORES.LIMPAR_UGS_ORGAOS
          );
        };

        vm.validarRipFusao = function () {
          var ripImovelRemembrado = vm.imovel.incorporacao.ripImovelRemembrado;
          if (
            ripImovelRemembrado &&
            ripImovelRemembrado.length === TAMANHO_RIP_VALIDO
          ) {
            ValidaRipFusaoService.get({
              rip: ripImovelRemembrado,
            }).$promise.then(function (response) {
              if (response.isValid) {
                vm.imovelRemembramento = response.imovel;
                vm.imovel.proprietario = response.imovel.proprietario;
                vm.imovel.proprietario.isIntegralmente = response.imovel
                  .proprietario.isIntegralmente
                  ? 'true'
                  : 'false';

                NotificadorService.notify(
                  $scope,
                  {
                    ugProprietario: vm.imovel.proprietario.unidadeUsuario,
                    orgaoProprietario: vm.imovel.proprietario.orgaoEntidade,
                  },
                  NOTIFICADORES.SETAR_PROPRIETARIO_FUSAO
                );
              } else {
                if (response.mensagem){
                  MensagensService.exibirMensagemErro(response.mensagem);
                } else {
                  MensagensService.exibirMensagemErro('RIP de Origem não está apto para essa operação');
                }
                vm.imovel.incorporacao.ripImovelRemembrado = undefined;
              }
            });
          } else {
            vm.imovelRemembramento = undefined;
          }
        };

        vm.incluirRip = function () {
          if (
            vm.imovelRemembramento &&
            !vm.imovel.ripsFusao.includes(vm.imovelRemembramento)
          ) {
            vm.imovel.ripsFusao.push(vm.imovelRemembramento);
          }
        };

        vm.removerRip = function (rip) {
          var indexRip = vm.imovel.ripsFusao.indexOf(rip);

          var confirm = $mdDialog
            .confirm({ focusOnOpen: false })
            .textContent('Tem certeza que deseja remover este RIP?')
            .ariaLabel('Excluir RIP')
            .targetEvent(indexRip)
            .cancel('Não')
            .ok('Excluir');

          $mdDialog.show(confirm).then(
            function () {
              vm.imovel.ripsFusao.splice(indexRip, 1);
              excluiBenfeitoriasRipExcluido(rip);
              MensagensService.exibirMensagemSucesso(
                'RIP excluído com sucesso.'
              );
              $mdDialog.hide();
            },
            function () {
              $mdDialog.cancel();
            }
          );
        };

        vm.resetarCertidaoCartorial = function () {
          vm.certidaoCartorial = {};
        };

        vm.showUploadCertidaoCartorialDialog = function (ev) {
          $mdDialog.show({
            controller: 'UploadCertidaoCartorialDialogController',
            templateUrl:
              'scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/templates/dialog-upload-certidao-cartorial.html',
            parent: angular.element($document[0].body),
            targetEvent: ev,

            clickOutsideToClose: false,
            locals: {
              certidaoCartorial: vm.certidaoCartorial,
              certidoesCartorial: vm.imovel.incorporacao.certidoesCartoriais,
            },
          });
        };

        vm.visualizarCertidao = function (id) {
          ExibirCertidao.visualizarCertidao(id);
        };

        vm.removerCertidao = function (index) {
          var confirm = $mdDialog.confirm({
            focusOnOpen: false,
            textContent: 'Deseja realmente excluir este arquivo?',
            ok: 'Excluir',
            cancel: 'Não',
          });

          $mdDialog.show(confirm).then(
            function () {
              vm.imovel.incorporacao.certidoesCartoriais.splice(index, 1);
              MensagensService.exibirMensagemSucesso(
                'Certidão excluída com sucesso.'
              );
              $mdDialog.hide();
            },
            function () {
              $mdDialog.cancel();
            }
          );
        };

        NotificadorService.subscribe(
          $scope,
          function () {
            vm.imovel.ripsFusao = [];
            angular.forEach(vm.imovel.benfeitorias, function (
              benfeitoria,
              index
            ) {
              if (benfeitoria.id) {
                vm.imovel.benfeitorias.splice(index, 1);
              }
            });
          },
          NOTIFICADORES.LIMPA_DADOS_RIPS_FUSAO
        );

        vm.isDetalhar = function () {
          return vm.action === 'detail';
        };

        vm.isEditar = function () {
          if(!$scope.imovel.migracaoSpiunet){
          return vm.action === 'edit';
          }else{
            return false;
          }
        };

        vm.buscaProcessoSEI = function () {
          vm.exibeMengSei = false;
          vm.imovel.linkProcessoSei = null;
          if (vm.imovel.numeroProcesso != null &&
              vm.imovel.numeroProcesso != undefined &&
            vm.imovel.numeroProcesso.trim() !== '' &&
            vm.imovel.tipoProcessoSei === 'processoExistente') {

            IntegracaoSeiService.recuperarProcedimentoSei(
              onlyNumbers(vm.imovel.numeroProcesso),
              vm.imovel.enderecamento.uf)
              .then(function (resp) {
                if(resp.data.mensagemErro != null) {
                  vm.exibeMengSei = true;
                  MensagensService.exibirMensagemErro(
                    resp.data.mensagemErro + ' A ausência de integração não impede o cadastro do imóvel.'
                  );
                } else {
                  vm.imovel.linkProcessoSei = resp.data.linkAcesso;
                  if(vm.imovel.id !== null && vm.imovel.id !== undefined && vm.imovel.numeroProcesso !== null){
                      MensagensService.exibirMensagemSucesso(' Processo encontrado no SEI.');
                  }
                  vm.exibeMengSei = false;
                }
              })
              .catch(function (err) {
                console.log(err)
                vm.imovel.linkProcessoSei = null;
                vm.exibeMengSei = true;
                if (ctrlDadosIncorporacao.usuarioLogado.organizacao.includes('Patrimônio da União')) {
                              MensagensService.exibirMensagemErro(
                              'Você não possui acesso ao processo informado (Processo restrito ou não está aberto na sua unidade SEI). A ausência de integração não impede o cadastro do imóvel.'
                            );

                 } else {

                    if (!vm.action) {
                      $mdDialog.show({
                        controller: modalProcessoSeiSPU,
                        templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-processoSei.html',
                        parent: angular.element(document.body),
                        clickOutsideToClose: false,
                      });
                    }
                 }

                if(!!$scope.imovel.migracaoSpiunet){
                  vm.blockNumProcesso = false;
                }
              });
          }


        };

        vm.irParaDetalhamento = function (id) {
          var url = $state.href('INCORPORACAO.detalharImovel', { id: id });
          $window.open(url, '_blank');
        };

        var modalProcessoSeiSPU = modalProcessoSeiSPU;

        modalProcessoSeiSPU.$inject = [
            '$scope',
            '$mdDialog'
        ];

        function modalProcessoSeiSPU(
            $scope,
            $mdDialog
        ) {

          $scope.fecharModal = function() {
            $mdDialog.hide();
          }
        }


        vm.resetNumeroProcesso = function () {
            vm.imovel.linkProcessoSei = null;
            vm.imovel.numeroProcesso = null;
            vm.exibeMengSei = false;
            if(vm.imovel.tipoProcessoSei === 'criarProcesso' && vm.isEditar() ){
              vm.blockNumProcesso = true;
            }else{
              vm.blockNumProcesso = false;
            }
        };

        vm.verificarRestricao = function () {
          if (vm.imovel && vm.imovel.rip && vm.imovel.incorporacao.tipoIncorporacao){
            if (vm.imovel.incorporacao.tipoIncorporacao.id == ID_FORMA_INC_FRACIONAMENTO){
              ValidaRipFracionamentoService.get({rip: vm.imovel.rip}).$promise.then(function (response) {
                if (!response.isValid) {
                  MensagensService.exibirMensagemErro("RIP suspenso com restrição para essa operação");
                  vm.imovel.incorporacao.tipoIncorporacao = undefined;
                }});
            } else if (vm.imovel.incorporacao.tipoIncorporacao.id == ID_FORMA_INC_FUSAO){
              ValidaRipFusaoService.get({rip: vm.imovel.rip}).$promise.then(function (response) {
                if (!response.isValid) {
                  vm.imovel.incorporacao.tipoIncorporacao = undefined;
                  MensagensService.exibirMensagemErro("RIP suspenso com restrição para essa operação");
                }});
            }
      }
    };

        function onlyNumbers(string) {
          if (!string) {
            return '';
          }
          return string.replace(/[^0-9]/g, '');
        }

        NotificadorService.subscribe(vm, function(evento, escopo, tabIndex) {
          if (tabIndex == ABA_DADOS_GERAIS){
            if (ctrlDadosIncorporacao.titulos){
              ctrlDadosIncorporacao.titulos.splice(0, ctrlDadosIncorporacao.titulos.length);
            }
            vm.imovel.incorporacao = {tipoAquisicao: undefined, tipoContratoPessoal: undefined, matriculas: [], certidoesCartoriais:[]};
          }
        }, NOTIFICADORES.LIMPAR_ABA);


        NotificadorService.subscribe($scope,function(evento, escopo, titulos) {
              ctrlDadosIncorporacao.titulos = titulos;
        },NOTIFICADORES.ADICIONAR_TITULO);


        init();
      },
    };
  }
})();

(function () {
  'use strict';
  angular
    .module('su-cadastro-imovel')
    .directive('secaoClassificacaoUtilizacao', classificacaoUtilizacai);

  function classificacaoUtilizacai() {
    return {
      templateUrl:
        'scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/templates/classificacao-utilizacao.html',
      scope: {
        imovel: '=',
        isDetail: '='
      },
      link: function ($scope) {
        var vm = $scope;

        vm.isDetalhar = function () {
          return vm.isDetail;
        };

        vm.isAlteracaoImovel = function () {
          return vm.imovel.id != null;
        };
      }
    };
  }
})();

(function(){
    "use strict";
    angular.module("su-cadastro-imovel").controller("UploadCertidaoCartorialDialogController",
        [ '$scope', '$http', '$mdDialog', 'certidaoCartorial', 'certidoesCartorial',
            '$sce', 'CertidaoCartorial', 'ExibirCertidao', 'verificaObrigatorios', 'MensagensService',

            function UploadCertidaoCartorialDialogController($scope, $http, $mdDialog, certidaoCartorial, certidoesCartorial,
                                                             $sce, CertidaoCartorial, ExibirCertidao, verificaObrigatorios, MensagensService){
                $scope.certidaoCartorial = certidaoCartorial;
                $scope.certidoesCartorial = certidoesCartorial;
                $scope.errorsForms=[];

                $scope.fechar = function () {
                    $mdDialog.cancel();
                };

                $scope.incluir = function(){
                    $scope.certidaoCartorialSubmitted = true;
                    if($scope.uploadCertidaoCartorial.$valid && validarExtensaoArquivo($scope.certidaoCartorial.certidao)){
                        if(validarCertidaoUnica($scope.certidaoCartorial)){
                            salvarArquivo($scope.certidaoCartorial);
                            $scope.certidaoCartorialSubmitted = false;
                        }
                    }
                }

                var salvarArquivo = function (certidao) {
                    CertidaoCartorial.save(certidao,
                        function (response) {
                            $scope.certidoesCartorial.push(response);
                            $scope.certidaoCartorial = {};
                        },
                        function (error) {
                            MensagensService.exibirMensagemErro(error.data.message);
                        })
                };

                var validarCertidaoUnica = function(objCertidao){
                    if($scope.certidoesCartorial.length <= 0){
                        return true;
                    }else{
                        for(var i = 0; i  < $scope.certidoesCartorial.length; i++){
                            if($scope.certidoesCartorial[i].nome == $scope.certidaoCartorial.nome){
                                MensagensService.exibirMensagemErro("O documento selecionado já foi enviado.");
                                return false;
                            }
                        }
                        return true;
                    }
                };

                $scope.removerCertidao = function(index){

                    var confirm = $mdDialog.confirm({
                        focusOnOpen : false,
                        textContent : "Deseja realmente excluir este arquivo?",
                        ok : "Excluir",
                        cancel : "Não"
                    });

                    $mdDialog.show(confirm).then(function(){
                        $scope.certidoesCartorial.splice(index, 1);
                        MensagensService.exibirMensagemSucesso("Certidão excluída com sucesso.");
                        $mdDialog.hide();
                    }, function(){
                        $mdDialog.cancel();
                    });

                };

                var validarExtensaoArquivo = function(arquivo){
                    if(getExtensao(arquivo.name) == ".pdf") return true;
                    else if(getExtensao(arquivo.name) == ".png") return true;
                    else if(getExtensao(arquivo.name) == ".jpg") return true;
                    else {

                        MensagensService.exibirMensagemErro("Formato de arquivo inválido. São permitidos apenas arquivos do tipo PDF, JPG ou PNG.");
                        return false;
                    }
                }

                $scope.hoje = new Date();

                var getExtensao = function(nomeArquivo){
                    return nomeArquivo.substring(nomeArquivo.length - 4);
                }

                $scope.confirmar = function () {
                    $mdDialog.hide();
                };

                $scope.visualizarCertidao = function(id){
                    ExibirCertidao.visualizarCertidao(id);
                }
            }])

})();

(function () {
    "use strict";
    directive.$inject = ["$filter", "ArquivoService", "$window", "MensagensService"];
    angular.module("su-cadastro-imovel").directive("formDocumento", directive);

    function directive($filter, ArquivoService, $window, MensagensService) {

        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/dados-documentacao/templates/form-documento.html",
            scope: {
                documento: "=",
                showFormDocumento: "=",
                showInclusaoBenfeitoria: "=",
                documentoBackup: "=",
                documentos: "=",
                isBenfeitoria: "="

            },
            link: function ($scope) {
                var vm = $scope;
                vm.today = new Date();
                vm.submittedFormDocumentos = false;

                vm.cancelarInclusaoDocumento = function () {
                    vm.showFormDocumento = false;
                    if (vm.isBenfeitoria) {
                        vm.showInclusaoBenfeitoria = true;
                    }
                    limparDadosDocumento();
                };

                vm.isPossuiDocumento = function () {
                  return vm.documento && vm.documento.possui === 'SIM';
                };

                vm.incluirDocumento = function () {
                    if (validarFormulario()){
                        vm.documento.dataEmissao = vm.documento.dataEmissao ? $filter('date')(vm.documento.dataEmissao, 'dd/MM/yyyy') : undefined;
                        vm.documento.dataValidade = vm.documento.dataValidade ? $filter('date')(vm.documento.dataValidade, 'dd/MM/yyyy') : undefined;
                        if (angular.isDefined(vm.documento.index)) {
                            vm.documentos.splice(vm.documento.index, 1, vm.documento);
                            MensagensService.exibirMensagemSucesso("Documento editado com sucesso!");
                        } else {
                            vm.documentos.push(vm.documento);
                            MensagensService.exibirMensagemSucesso("Documento incluído com sucesso!");
                        }
                        if (vm.isBenfeitoria) {
                            vm.showInclusaoBenfeitoria = true;
                        }
                        vm.showFormDocumento = false;
                        vm.submittedFormDocumentos = false;
                        limparDadosDocumento();
                    } else {
                        vm.submittedFormDocumentos = true;
                        vm.forms.inclusaoDocumentoForm.$setSubmitted();
                    }

                };

                function limparDadosDocumento () {
                    vm.documento = {};
                    vm.forms.inclusaoDocumentoForm.$setPristine();
                }

                function validarFormulario () {
                    if (vm.forms.inclusaoDocumentoForm.$valid) {
                        if (angular.isUndefined(vm.documento.possui) || vm.documento.possui == null) {
                            MensagensService.exibirMensagemCamposObrigatorios();
                            return false;
                        }

                        if (vm.documento.dataValidade && (vm.documento.dataValidade < vm.documento.dataEmissao)) {
                            MensagensService.exibirMensagemErro("Período do documento inválido.");
                            return false;
                        }

                        return true;

                    } else {
                        MensagensService.exibirMensagemCamposObrigatorios();
                        return false;
                    }
                }

                vm.uploadArquivo = function() {

                   if (!isTamanhoValido()) {
                        MensagensService.exibirMensagemErro("Arquivo inválido! São permitidos arquivos com 5MB.");
                    } else {
                        ArquivoService.uploadArquivo(vm.documento.arquivo).then(
                            function (data) {
                                vm.documento.arquivo.arquivo = data.data.byteArray;
                                vm.documento.arquivo.nomeOriginal = data.data.nome;
                                vm.documento.arquivo.tipoArquivo = data.data.tipo;
                                gerarUrlDocumento(vm.documento);
                            }
                        );
                    }
                };

                function isFormatoValido () {
                    //DOC, PDF, DOCX, ODS, TXT
                    var formatosValidos = ["application/msword",
                        "application/pdf",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                        "application/vnd.oasis.opendocument.spreadsheet",
                        "text/plain"];
                    return formatosValidos.includes(vm.documento.arquivo.type);
                }

                function isTamanhoValido () {
                    return vm.documento.arquivo.size <= 5000000;
                }

                function gerarUrlDocumento (documento) {
                    if (documento.arquivo && documento.arquivo.arquivo) {
                        var url = $window.URL || $window.webkitURL;
                        var blob = new Blob([base64ToArrayBuffer(documento.arquivo.arquivo)], {type: 'application/octet-stream'});
                        documento.url = url.createObjectURL(blob);
                    }
                }


                function base64ToArrayBuffer (base64) {
                    var binaryString = $window.atob(base64);
                    var binaryLen = binaryString.length;
                    var bytes = new Uint8Array(binaryLen);
                    for (var i = 0; i < binaryLen; i++) {
                        var ascii = binaryString.charCodeAt(i);
                        bytes[i] = ascii;
                    }
                    return bytes;
                }

            }

        };
    }
})();

(function () {
    "use strict";
    directive.$inject = ["$mdDialog", "$window", "$http", "urlBase", "MensagensService", "PaginacaoFactory", "NotificadorService", "NOTIFICADORES"];
    angular.module("su-cadastro-imovel").directive("dadosDocumentacao", directive);

    function directive($mdDialog, $window, $http, urlBase, MensagensService, PaginacaoFactory, NotificadorService, NOTIFICADORES) {
        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/dados-documentacao/templates/dados-documentacao.html",
            scope: {
                model: "=",
                documento: "=",
                documentoBackup: "=",
                showTabFormDocumento: "=",
                showTabInclusaoBenfeitoria: "=",
                documentosInc: "=",
                action: "@",
                detail: "=",
                desabilitar: "="
            },
            require: ['^?inclusaoBenfeitoria', '^?dadosEdificacaoUnidadeAutonoma'],
            link: function ($scope, element, attrs, controllers) {

                var vm = $scope;
                vm.ctrl = controllers[0] || controllers[1];
                vm.ordenacao = null;
                vm.paginacao = PaginacaoFactory.getDefault();
                vm.documentosReaproveitados = false;

                vm.tabelaDocumentos = {
                    limit: 10,
                    page: 1,
                    limitOptions: [10, 25, 30]
                };

                function init(){
                    angular.forEach(vm.model, function (documento) {
                        if (documento && documento.arquivo && documento.arquivo.arquivo) {
                            gerarUrlDocumento(documento);
                        }
                        if (documento && !isEmpty(documento.possui) && angular.isObject(documento.possui)) {
                            documento.possui = documento.possui.name;
                        }

                    });
                }

                $scope.$watch('model', function() {
                  init();
                });

                function isEmpty(value) {
                    return angular.equals({}, value) || angular.isUndefined(value) || value === null || value === "";
                }

                vm.exibeTab = function (documento) {
                    vm.showTabFormDocumento = true;
                    vm.showTabInclusaoBenfeitoria = false;

                    if(documento){
                      documento.index = vm.model.indexOf(documento);
                    }

                    if(vm.action === 'benfeitoria'){
                      vm.ctrl.exibeTabFormDocumento(vm.showTabFormDocumento,  vm.showTabInclusaoBenfeitoria , documento);
                    }else{
                      vm.$parent.exibeTabFormDocumento(vm.showTabFormDocumento,  vm.showTabInclusaoBenfeitoria , documento);
                      //Manda o documento pro form (necessário apenas no incluir UA)
                      NotificadorService.notify($scope, documento, NOTIFICADORES.EDITAR_DOCUMENTO_UA);
                    }
                };

                $scope.$on('atualizaDocumentacaoUnidadeAutonoma', function(event, args) {
                  var documentacao = args.documentacao;
                  vm.documento = documentacao;
                  vm.documentosReaproveitados = true;
              });


              function gerarUrlDocumento(documento) {
                  if (documento.arquivo && documento.arquivo.arquivo) {
                      var url = $window.URL || $window.webkitURL;
                      var blob = new Blob([base64ToArrayBuffer(documento.arquivo.arquivo)], {type: 'application/octet-stream'});
                      documento.url = url.createObjectURL(blob);
                  }
              }

              function base64ToArrayBuffer (base64) {
                var binaryString = $window.atob(base64);
                var binaryLen = binaryString.length;
                var bytes = new Uint8Array(binaryLen);
                for (var i = 0; i < binaryLen; i++) {
                    var ascii = binaryString.charCodeAt(i);
                    bytes[i] = ascii;
                }
                return bytes;
              }


                vm.excluirDocumento = function (ev, documento) {
                    var confirm = $mdDialog.confirm({
                        focusOnOpen: false
                    })
                        .textContent("Deseja confirmar a exclusão do documento?")
                        .targetEvent(ev)
                        .cancel("Não")
                        .ok("Sim");

                    $mdDialog.show(confirm).then(
                        function () {
                            var index = vm.model.indexOf(documento);
                            vm.model.splice(index, 1);
                        }
                    );
                };

                vm.visualizarDocumento = function (documento) {
                  window.open(documento.url)
                }

                vm.dadosDocumentacaoDisabled = function () {
                    return isDetalhamento();
                };

                function isDetalhamento() {
                    return vm.detail || vm.documentosReaproveitados || vm.desabilitar;
                }

                init ();
            }
        }
    }
})();

(function () {
    "use strict";
    directive.$inject = ["PadraoAcessibilidade"];
    angular.module("su-cadastro-imovel").directive("condicoesAcessibilidade", directive);

    function directive(PadraoAcessibilidade) {
        return {
            restrict: "E",
            templateUrl: "scripts-cadastro-imovel/app/directives/secoes-formulario/condicoes-acessibilidade/templates/condicoes-acessibilidade.html",
            scope: {
                model: "=",
                unidadeAutonoma: "=",
                possuiElevador: "=?",
                isGaragem: "=?",
                action: "@",
                desabilitar: "="
            },

            link: function ($scope) {

                var vm = $scope;
                vm.clicou = false;

                //vm.unidadeAutonoma = vm.imovel.unidadeAutonoma;
                vm.constantesAcessibilidade = PadraoAcessibilidade.query();

                if(vm.action == 'detail'){
                  vm.desabilitarCheckBox = true;
                }else{
                  vm.desabilitarCheckBox = false;
                }

                vm.updateInformacoesAcessibilidade = function() {
                  vm.clicou = true;
                    if (vm.model.semInformacoesAcessibilidade === true) {

                        //vm.model.vaga = undefined;
                        //vm.model.mobilidade = undefined;
                        //vm.model.banheiro = undefined;
                        //vm.model.elevador = undefined;
                        vm.model.vagasDefFisicos = vm.constantesAcessibilidade.id = null;
                        vm.model.vagasIdosos = vm.constantesAcessibilidade.id = null;
                        vm.model.calcadasRebaixa = null;
                        vm.model.calcadasLivres = null;
                        vm.model.entradaLivre = null;
                        vm.model.pavimentosLivreCadRodas = null;
                        vm.model.balcaoInformacoes = null;
                        vm.model.pisoTatil = null;
                        vm.model.corredorLargo = null;
                        vm.model.sanitarioPublico = null;
                        vm.model.braile = null;
                        vm.model.libras = null;
                        vm.model.possuiLaudo = null;
                        vm.model.linkLaudo = null;
                        vm.model.possuiPlano = null;
                        vm.model.linkPlano = null;
                        vm.model.linkPlanoTrabalho = null;
                        vm.model.possuiPlanoTrabalho = null;

                        vm.inputDisabledPlano();
                        vm.inputDisabledLaudo();
                        vm.action = 'detail';

                    }else{
                        vm.action = 'edit';
                    }
                };


            vm.atualizarLinkLaudo = function () {
                if (vm.model.possuiLaudo && vm.model.possuiLaudo.id !== 2) {
                   vm.model.linkLaudo = null;
                }
                vm.inputDisabledLaudo();
            };

            vm.atualizarLinkPlano = function () {
                if (vm.model.possuiPlano && vm.model.possuiPlano.id !== 2) {
                   vm.model.linkLaudo = null;
                 }
                 vm.inputDisabledPlano();
            };


            vm.condicoesAcessibilidadeDisabled = function () {
                    return isDetalhamento() || vm.desabilitar;
                };

            vm.inputDisabled = function () {
                    return isDetalhamento() || vm.model.semInformacoesAcessibilidade;
                };

            vm.inputDisabledLaudo = function () {

              if (vm.model.possuiLaudo) {
                return vm.model.possuiLaudo.id !== 2 || isDetalhamento();
              }
                 return isDetalhamento();
              };

            vm.inputDisabledPlano = function () {

                if (vm.model.possuiPlanoTrabalho) {
                  return vm.model.possuiPlanoTrabalho.id !== 2 || isDetalhamento();
                }

                return isDetalhamento();
            };

            function isDetalhamento() {
                    return vm.action === 'detail';
                }

            $scope.$on('atualizaCondicoesAcessibUnidadeAutonoma', function(event, args) {
                var condicoesAcessibilidade = args.condicoesAcessibilidade;
                vm.clicou = false;
                if(condicoesAcessibilidade.semInformacoesAcessibilidade === true){
                  vm.model.semInformacoesAcessibilidade = true;
                }else{
                  vm.model.vagasDefFisicos = condicoesAcessibilidade.vagasDefFisicos;
                  vm.model.vagasIdosos = condicoesAcessibilidade.vagasIdosos
                  vm.model.calcadasRebaixa = condicoesAcessibilidade.calcadasRebaixa;
                  vm.model.calcadasLivres = condicoesAcessibilidade.calcadasLivres;
                  vm.model.entradaLivre = condicoesAcessibilidade.entradaLivre;
                  vm.model.pavimentosLivreCadRodas = condicoesAcessibilidade.pavimentosLivreCadRodas;
                  vm.model.balcaoInformacoes = condicoesAcessibilidade.balcaoInformacoes;
                  vm.model.pisoTatil = condicoesAcessibilidade.pisoTatil;
                  vm.model.corredorLargo = condicoesAcessibilidade.corredorLargo;
                  vm.model.sanitarioPublico = condicoesAcessibilidade.sanitarioPublico;
                  vm.model.braile = condicoesAcessibilidade.braile;
                  vm.model.libras = condicoesAcessibilidade.libras;
                  vm.model.possuiLaudo = condicoesAcessibilidade.possuiLaudo;
                  vm.model.linkLaudo = condicoesAcessibilidade.linkLaudo;
                  vm.model.possuiPlano = condicoesAcessibilidade.possuiPlano;
                  vm.model.linkPlano = condicoesAcessibilidade.linkPlano;
                  vm.model.linkPlanoTrabalho = condicoesAcessibilidade.linkPlanoTrabalho;
                  vm.model.possuiPlanoTrabalho = condicoesAcessibilidade.possuiPlanoTrabalho;
                }
            });

          }
        }
    }
})();

(function () {
    'use strict';
    /*global angular*/
angular.module("su-cadastro-imovel")
    .factory("PontosService", ["$resource", "urlBase", function($resource, urlBase){
		return $resource(urlBase + "imovel/pontos");
    }])
    .factory("TerrenoCadastralService", ["$resource", "urlBase", function($resource, urlBase){
        return $resource(urlBase + 'cartografia/terrenos-cadastrais');
    }])
    .factory("TerrenoCartorialService", ["$resource", "urlBase", function($resource, urlBase){
        return $resource(urlBase + 'cartografia/terrenos-cartoriais');
    }])
    .factory("TerrenoBenfeitoriaService", ["$resource", "urlBase", function($resource, urlBase){
        return $resource(urlBase + 'cartografia/benfeitorias');
    }])
    .factory("PontosUnidadeAutonomaService", ["$resource", "urlBase", function($resource, urlBase){
        return $resource(urlBase + "cartografia/pontos-unidade-autonoma");
    }]);
}());

(function () {
    'use strict';
    /*global angular*/
    angular.module('ciMapaDirective', []);
}());
(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('ciMapaDirective').factory('ciMapaViewFactory', ["MAPA_CONSTANTS_INCORPORACAO", function (MAPA_CONSTANTS_INCORPORACAO) {

        var view,
            init;

        init = function () {
            view = new ol.View({
                center: ol.proj.fromLonLat(MAPA_CONSTANTS_INCORPORACAO.GEOMETRY.GEOMETRIA_INICIAL),
                zoom: 4,
                maxZoom: 19,
                minZoom: 1
            });
        };

        init();

        return {
            getView: function () {
                return view;
            },

            centralizarMapa: function (geometry, size) {
                view.fit(geometry, size);
            },

            init: init
        };
    }]);
}());

(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('ciMapaDirective').factory('ciMapaValidacaoFactory', function () {
      //Quando voltar a usar essa classe, precisa renomear pois há conflito com o módulo avaliação

        var geoJson = {};

        var vm = this,
        validaCepPontoMunicipio;

        validaCepPontoMunicipio = function (geomMunicipio, coordPont){

            geoJson.type = 'FeatureCollection';
            geoJson.crs = {
                      'type': 'name',
                      'properties': {
                        'name': 'EPSG:3857'
                      }};

            var response;
            if(geomMunicipio.coordinates != undefined){
                geoJson.features = [{
                    'type': 'Feature',
                    'geometry': {"type":"MultiPolygon",
                    'coordinates': geomMunicipio.coordinates}
                }];

                var feature = (new ol.format.GeoJSON()).readFeatures(geoJson);
                var geometria = feature[0].getGeometry();
                if(geometria.intersectsCoordinate(coordPont)){
                    response = true;
                }else{
                    response = false;
                }
            }
            return response;
        }

        return {
            validaCepPontoMunicipio: validaCepPontoMunicipio
        }
    });
}());

(function() {
    "use strict";
    /*global ol*/
    angular.module("ciMapaDirective").factory("ciMapaStyleFactory", function() {

        function PinCinzaUA() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-cinza-32-UA.png'
                }),
                text: new ol.style.Text({
                    font: "bold 15px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                })
            });
        }

        function PinCinza() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-cinza-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                })
            });
        }

        function PinAmarelo() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-amarelo-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function PinVermelho() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-vermelho-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function PinVerde() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-verde-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function PinGreenYellow() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-verde-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: 'greenyellow'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "greenyellow"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function PinVerdeTestada() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-verde-testada-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function TerrenoCinza() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(171, 171, 171, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: 'rgba(171, 171, 171, 1)',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoCinzaEscuro() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(100, 100, 100, 0.5)'
                }),
                stroke: new ol.style.Stroke({
                    color: 'rgba(120, 120, 120, 1)',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoAmarelo() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(255, 222, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#ffde00',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoAmareloPontilhado() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(255, 222, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#ffde00',
                    width: 3,
                    lineDash: [4]
                }),
                zIndex: 1
            });
        }

        function TerrenoVerde() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(20, 236, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#14ec00',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoVerdePontilhado() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(20, 236, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#14ec00',
                    width: 3,
                    lineDash: [4]
                }),
                zIndex: 1
            });
        }

        function TerrenoVermelho() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(255, 0, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#ff0000',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoAzul() {
          return new ol.style.Style({
              fill: new ol.style.Fill({
                  color: 'rgba(5, 43, 130, 0.2)'
              }),
              stroke: new ol.style.Stroke({
                  color: '#052b82',
                  width: 2
              }),
              zIndex: 1
          });
      }

        function TerrenoRoxo() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: '#6c009e'
                }),
                stroke: new ol.style.Stroke({
                    color: '#6c009e'
                }),
                zIndex: 1
            });
        }

        function CirculoCinza() {
            return new ol.style.Style({
                image: new ol.style.Circle({
                    radius: 15,
                    stroke: new ol.style.Stroke({
                        color: '#fff'
                    }),
                    fill: new ol.style.Fill({
                        color: '#777777'
                    })
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    })
                })
            });
        }


        return {
            pinCinza: function() {
                return new PinCinza();
            },
            pinCinzaUA: function() {
                return new PinCinzaUA();
            },
            pinAmarelo: function() {
                return new PinAmarelo();
            },
            pinVermelho: function() {
                return new PinVermelho();
            },
            pinVerde: function() {
                return new PinVerde();
            },
            pinVerdeTestada: function() {
                return new PinVerdeTestada();
            },
            pinGreenYellow: function() {
                return new PinGreenYellow();
            },
            terrenoCinza: function() {
                return new TerrenoCinza();
            },
            terrenoCinzaEscuro: function() {
                return new TerrenoCinzaEscuro();
            },
            terrenoAmarelo: function() {
                return new TerrenoAmarelo();
            },
            terrenoAmareloPontilhado: function() {
                return new TerrenoAmareloPontilhado();
            },
            terrenoVerde: function() {
                return new TerrenoVerde();
            },
            terrenoVerdePontilhado: function() {
                return new TerrenoVerdePontilhado();
            },
            terrenoVermelho: function() {
                return new TerrenoVermelho();
            },
            terrenoAzul: function() {
              return new TerrenoAzul();
            },
            terrenoRoxo: function() {
                return new TerrenoRoxo();
            },
            circuloCinza: function() {
                return new CirculoCinza();
            }
        }
    });
})();

/* eslint-disable angular/log */
(function () {
  'use strict';
  /*global angular*/
  /*global ol*/
  angular.module('ciMapaDirective').factory('ciMapaLayersFactory',
   ["NotificadorService", "MAPA_CONSTANTS_INCORPORACAO", "PontosService", "TerrenoCadastralService", "TerrenoCartorialService", "TerrenoBenfeitoriaService", "ciMapaStyleFactory", "comumService", "PontosUnidadeAutonomaService", "$http", "$q", "urlBase", function (NotificadorService, MAPA_CONSTANTS_INCORPORACAO, PontosService, TerrenoCadastralService, TerrenoCartorialService, TerrenoBenfeitoriaService, ciMapaStyleFactory, comumService, PontosUnidadeAutonomaService, $http, $q, urlBase) {

    var vm = this,
      init,
      camadasMapa,
      camadasFinal,
      camadasMapaImovelHomologacao,
      camadasMinimapa,
      osm,
      bingMapa,
      bingMinimapa,
      addGeoServerTileLayer,
      addGeoServerVectorLayer,
      addLayersImovelMapaHomologacao,
      ortofotos,
      sourcesGeoServer = [],
      getGeoServerTileWMSSource,
      getGeoServerVectorSource,
      setPontoImovelLayer,
      setPontoUALayer,
      clusterSource,
      clusterSourceUA,
      styleCache,
      styleCacheUA,
      clusters,
      clustersUA,
      geoServerTileWMS = [],
      setPontoImovelSource,
      setPontoUASource,
      buscaPontos,
      buscaPontosUA,
      styles = ciMapaStyleFactory,
      terrenoCadastralSource,
      terrenoCartorialSource,
      terrenoBenfeitoriaSource,
      setTerrenoCadastralSource,
      setTerrenoCartorialSource,
      setTerrenoBenfeitoriaSource,
      urlGeoWebCache,
      urlGeoServer,
      pontoImovelSource,
      pontoUASource;


    init = function (ativarPontos) {
      camadasMapa = new ol.Collection();
      camadasMapaImovelHomologacao = new ol.Collection();
      camadasFinal = new ol.Collection();
      camadasMinimapa = new ol.Collection();
      comumService.buscarUrlGeoServer().then(function (retorno) {
        vm.setOrtofotoLayer();
        urlGeoServer = retorno.data.url;
        getGeoServerTileWMSSource();
        getGeoServerVectorSource();
        if(ativarPontos){
          setPontoImovelSource();
          setPontoUASource();
        }
      });
      vm.setOsm();
      vm.setBingMapa();
      vm.setBingMinimapa();
    };
    geoServerTileWMS = [
      {
        name: "UF",
        layer: "ide:uf",
        label: "UF",
        visible: true,
        required: true
      },
      {
        name: "municipio",
        layer: "ide:municipio",
        label: "Município",
        visible: true,
        required: true
      },
      {
        name: "areaUniao",
        layer: "ide:tb_areas_uniao",
        label: "Área da União",
        visible: false,
        required: false
      }
    ];

    vm.setOsm = function () {
      osm = new ol.layer.Tile({
        source: new ol.source.OSM(),
        name: 'osm',
        required: true
      });
      camadasMapa.push(osm);
      camadasMinimapa.push(osm);
      camadasMapaImovelHomologacao.push(osm);
      camadasFinal.push(osm);
    };

    vm.setBingMapa = function () {
      bingMapa = new ol.layer.Tile({
        source: new ol.source.BingMaps({
          key: 'AtwyeZCHS5j5qQT3JNFayXjp_8FvRtPWefOsSW5g6MFzMgPIRvzLt5BhfsDnppQ9',
          imagerySet: 'AerialWithLabels'
        }),
        visible: false,
        name: 'bingMapa',
        required: true
      });
      camadasMapa.push(bingMapa);
    };

    vm.setBingMinimapa = function () {
      bingMinimapa = new ol.layer.Tile({
        source: new ol.source.BingMaps({
          key: 'AtwyeZCHS5j5qQT3JNFayXjp_8FvRtPWefOsSW5g6MFzMgPIRvzLt5BhfsDnppQ9',
          imagerySet: 'AerialWithLabels'
        }),
        visible: true,
        name: 'bingMinimapa',
        required: true
      });
      camadasMinimapa.push(bingMinimapa);
    };
    vm.setOrtofotoLayer = function () {

      ortofotos = new ol.layer.Tile({
        source: new ol.source.TileWMS({
          url: urlGeoWebCache,
          params: {
            'layers': 'layerGroupBrasil',
            'srs': 'EPSG:900913',
            'styles': 'ortofoto',
            'TILED': true,
            'format': 'image/png',
            'transparent': false,
            'BBOX': '0.0, 0.0, -1.001875417E7, -1.001875417E7'
          }
        }),
        name: 'ortofotos',
        visible: false,
        label: 'Ortofotos e Ortofotocartas',
        required: false
      });
      camadasMapa.push(ortofotos);
    };

    addGeoServerTileLayer = function () {
      var i,
        layer;
      for (i = 0; i < geoServerTileWMS.length; i++) {
        layer = new ol.layer.Tile({
          source: sourcesGeoServer[geoServerTileWMS[i].name],
          name: geoServerTileWMS[i].name,
          visible: geoServerTileWMS[i].visible,
          label: geoServerTileWMS[i].label,
          required: geoServerTileWMS[i].required
        });

        camadasMapa.push(layer);
      }
    };

    addGeoServerVectorLayer = function (name, source, label, visible, required) {
      var layer = new ol.layer.Vector({
        source: source,
        style: function (feature) {
          if (feature.getProperties().unidadeAutonoma) {
            if (name === "benfeitoria") {
              return styles.terrenoVerde();
            } else {
              return styles.terrenoCinzaEscuro();
            }
          } else {
            return styles.terrenoCinza();
          }
        },
        name: name,
        label: label,
        visible: visible,
        required: required
      });
      camadasMapa.push(layer);
    };

      addLayersImovelMapaHomologacao = function (name, source, label, visible, required, condicaoImovel) {
          var layer = new ol.layer.Vector({
              source: source,
              style: function (feature) {
                  if (feature.getProperties().unidadeAutonoma) {
                      if (name === "benfeitoria") {
                          return styles.terrenoVerde();
                      } else {
                          return styles.terrenoCinzaEscuro();
                      }
                  } else {
                      return styles.terrenoCinza();
                  }
              },
              name: name,
              label: label,
              visible: visible,
              required: required
          });

          layer.condicaoImovel = condicaoImovel;
          camadasMapaImovelHomologacao.push(layer);
      };

    buscaPontos = function() {
      var dfd = $q.defer();
      $http.get(urlBase  + "imovel/pontos", {
        ignoreLoadingBar: true
      })
      .then(function(response) {
        dfd.resolve(response.data);
      });
       return dfd.promise;
    };

    buscaPontosUA = function() {
      var dfdUA = $q.defer();
      $http.get(urlBase  + "cartografia/pontos-unidade-autonoma", {
        ignoreLoadingBar: true
      })
      .then(function(response) {
        dfdUA.resolve(response.data);
      });
       return dfdUA.promise;
    };

    setPontoImovelSource = function () {
      pontoImovelSource = new ol.source.Vector({});
      setPontoImovelLayer();
      buscaPontos().then(function(data){
        var features = (new ol.format.GeoJSON({
          defaultProjection: 'EPSG:3857'
        })).readFeatures(data);
        pontoImovelSource.addFeatures(features);
      });
    };

    setPontoUASource = function () {
      pontoUASource = new ol.source.Vector({});
      setPontoUALayer();
      //PontosUnidadeAutonomaService.get(function (result) {
      buscaPontosUA().then(function(data){
        var features = (new ol.format.GeoJSON({
          defaultProjection: 'EPSG:3857'
        })).readFeatures(data);
        pontoUASource.addFeatures(features);
      });
    };

    setPontoImovelLayer = function () {
      clusterSource = new ol.source.Cluster({
        distance: 70,
        source: pontoImovelSource,
        projection: "EPSG:3857"
      });
      styleCache = [];
      clusters = new ol.layer.Vector({
        source: clusterSource,
        style: function (feature) {
          var size = feature.get('features').length;
          var style = styleCache[size];
          if (!style) {
            style = styles.pinCinza();
            if (size > 1) {
              style = styles.circuloCinza();
              style.getText().setText(size.toString());
            } else {
              style.getText().setText("");
            }
            styleCache[size] = style;
          }
          return style;
        },
        name: "pontoImovel",
        label: "Ponto do Imóvel",
        required: false,
        visible: true
      });
      camadasMapa.push(clusters);
    };

    setPontoUALayer = function () {
      clusterSourceUA = new ol.source.Cluster({
        distance: 70,
        source: pontoUASource,
        projection: "EPSG:3857"
      });
      styleCacheUA = [];
      clustersUA = new ol.layer.Vector({
        source: clusterSourceUA,
        style: function (feature) {
          var size = feature.get('features').length;
          var style = styleCacheUA[size];
          if (!style) {
            style = styles.pinCinzaUA();
            if (size > 1) {
              style.getText().setText(size.toString());
            } else {
              style.getText().setText("");
            }
            styleCacheUA[size] = style;
          }
          return style;
        },
        name: "pontoUnidadeAutonoma",
        label: "Ponto Unidade Autônoma",
        required: false,
        visible: true
      });
      camadasMapa.push(clustersUA);
    };

    getGeoServerTileWMSSource = function () {
      for (var i = 0; i < geoServerTileWMS.length; i++) {
        sourcesGeoServer[geoServerTileWMS[i].name] = new ol.source.TileWMS({
          url: urlGeoServer,
          params: {
            'layers': geoServerTileWMS[i].layer,
            'srs': 'EPSG:4674',
            'TILED': true
          }
        });
      }
      addGeoServerTileLayer();
    };

    getGeoServerVectorSource = function () {
      terrenoCadastralSource = new ol.source.Vector({
      });
      terrenoCartorialSource = new ol.source.Vector({
      });
      terrenoBenfeitoriaSource = new ol.source.Vector({
      });
      setTerrenoCadastralSource();
      setTerrenoCartorialSource();
      setTerrenoBenfeitoriaSource();
    };

    setTerrenoCadastralSource = function () {
      addGeoServerVectorLayer("terrenoCadastral", terrenoCadastralSource, "Terreno Cadastral", true, false);
      TerrenoCadastralService.get(function (result) {
        var features = (new ol.format.GeoJSON({
          defaultProjection: 'EPSG:3857'
        })).readFeatures(result);
        terrenoCadastralSource.addFeatures(features);
      });
    };

    setTerrenoCartorialSource = function () {
      addGeoServerVectorLayer("terrenoCartorial", terrenoCartorialSource, "Terreno Cartorial", true, false);
      TerrenoCartorialService.get(function (result) {
        var features = (new ol.format.GeoJSON({
          defaultProjection: 'EPSG:3857'
        })).readFeatures(result);
        terrenoCartorialSource.addFeatures(features);
      });
    };

    setTerrenoBenfeitoriaSource = function () {
      addGeoServerVectorLayer("benfeitoria", terrenoBenfeitoriaSource, "Benfeitoria", true, false);
      TerrenoBenfeitoriaService.get(function (result) {
        var features = (new ol.format.GeoJSON({
          defaultProjection: 'EPSG:3857'
        })).readFeatures(result);
        terrenoBenfeitoriaSource.addFeatures(features);
      });
    };

    return {

      getCamadas: function (isMinimapa) {
        if (isMinimapa) {
          return camadasMinimapa;
        } else {
          return camadasMapa;
        }
      },

        getCamadasHomologar: function (imovelFinal) {

            /**
             * Sempre utilizar o '-' para identificar se e Original ou Alterado
             */
            if (imovelFinal.rip != null) {
                if (imovelFinal.featuresOutrosImoveisSource) {
                    addLayersImovelMapaHomologacao("outrosImoveis", imovelFinal.featuresOutrosImoveisSource, "Outros imóveis", true, false, 'OT');
                }

                if (imovelFinal.terrenoCartorialSource.objetoPreenchido) {
                    addLayersImovelMapaHomologacao("terrenoCartorial-O", imovelFinal.terrenoCartorialSource, "Terreno Cartorial", true, false, 'O');
                }

                if (imovelFinal.terrenoCadastralSource.objetoPreenchido) {
                    addLayersImovelMapaHomologacao("terrenoCadastral-O", imovelFinal.terrenoCadastralSource, "Terreno Cadastral", true, false, 'O');
                }
            }

            if(imovelFinal.terrenoCartorialSourceAlterado.objetoPreenchido) {
                addLayersImovelMapaHomologacao("terrenoCartorial-A", imovelFinal.terrenoCartorialSourceAlterado, "Terreno Cartorial", true, false, 'A');
            }

            if(imovelFinal.terrenoCadastralSourceAlterado.objetoPreenchido) {
                addLayersImovelMapaHomologacao("terrenoCadastral-A", imovelFinal.terrenoCadastralSourceAlterado, "Terreno Cadastral", true, false , 'A');
            }

            if (imovelFinal.rip != null) {
                angular.forEach(imovelFinal.featuresBenfeitoriasSource, function (feature, index) {
                    var benfeitoria = new ol.source.Vector;
                    benfeitoria.addFeature(feature);
                    addLayersImovelMapaHomologacao("benfeitoria" + ++index + "-O", benfeitoria, "Benfeitoria " + index, true, false, 'O');
                });
            }

            angular.forEach(imovelFinal.featuresBenfeitoriasAlterado, function (feature, index) {
                var benfeitoria = new ol.source.Vector;
                benfeitoria.addFeature(feature);
                addLayersImovelMapaHomologacao("benfeitoria" + ++index + "-A", benfeitoria, "Benfeitoria " + index, true, false, 'A');
            });

            if(imovelFinal.unidadeAutonomaSourceAlterado.objetoPreenchido) {
                addLayersImovelMapaHomologacao("unidadeAutonoma-A", imovelFinal.unidadeAutonomaSourceAlterado, "Unidade Autonoma", true, false, 'A');
            }

            if (imovelFinal.rip != null) {
                if (imovelFinal.unidadeAutonomaSource.objetoPreenchido) {
                    addLayersImovelMapaHomologacao("unidadeAutonoma-O", imovelFinal.unidadeAutonomaSource, "Unidade Autonoma", true, false, 'O');
                }

                if (imovelFinal.pontoSource.objetoPreenchido) {
                    addLayersImovelMapaHomologacao("pontoO-O", imovelFinal.pontoSource, "Ponto", true, false, 'O');
                }
            }

            if(imovelFinal.pontoSourceAlterado.objetoPreenchido) {
                addLayersImovelMapaHomologacao("pontoA-A", imovelFinal.pontoSourceAlterado, "Ponto", true, false, 'A');
            }

            return camadasMapaImovelHomologacao
        },

      init: init
    };
  }]);
}());

(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('ciMapaDirective').directive('ciMinimapa', ["ciMapaViewFactory", "ciMapaLayersFactory", "NotificadorService", "MAPA_CONSTANTS_INCORPORACAO", function (ciMapaViewFactory, ciMapaLayersFactory, NotificadorService, MAPA_CONSTANTS_INCORPORACAO) {
        return {
            scope: {
            },
            template: '<div></div>',
            link: function (scope, element, attrs, ctrl) {
                angular.element(document).ready(function () {
                    var viewFac = ciMapaViewFactory,
                        layersFac = ciMapaLayersFactory,
                        layers = layersFac.getCamadas(true),
                        map,
                        notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES,
                        height,
                        width;

                    if (attrs.height && !isNaN(attrs.height)) {
                        height = parseInt(attrs.height, 10);
                    } else {
                        height = 100;
                    }
                    if (attrs.width && !isNaN(attrs.width)) {
                        width = parseInt(attrs.width, 10);
                    } else {
                        width = 100;
                    }

                    map = new ol.Map({
                        target: element[0].firstChild,
                        layers: layers,
                        view: viewFac.getView(),
                        controls: ol.control.defaults({
                            attribution: false,
                            zoom: false,
                            rotate: false
                        }),
                        interactions: ol.interaction.defaults({
                            mouseWheelZoom: false,
                            doubleClickZoom: false,
                            altShiftDragRotate: false,
                            shiftDragZoom: false,
                            dragPan: false
                        })
                    });

                    map.setSize([width, height]);

                    NotificadorService.subscribe(scope, function (event, scope, boolean) {
                        angular.forEach(map.getLayers(), function (value) {
                            if (value.get('name') === 'bingMinimapa') {
                                value.setVisible(!boolean);
                            }
                        });
                    }, notifiers.ALTERAR_VISIBILIDADE_BING);
                });
            }
        };
    }]);
}());

(function() {
  'use strict';
  /*global angular*/
  /*global ol*/
  angular.module('ciMapaDirective').directive('ciMapaIncorporacao', ["ciMapaViewFactory", "ciMapaLayersFactory", "NotificadorService", "MAPA_CONSTANTS_INCORPORACAO", "$state", "PontoImovel", "$rootScope", "$mdDialog", "ciMapaStyleFactory", "ValidaTerrenoBenfeitoriaService", "ImovelPontoService", "ImovelOverlayService", "$document", "$compile", "tabsIncorporacao", "UnidadeAutonomaService", "MensagensService", "MapaService", "obterTerrenosIntersedidos", function(ciMapaViewFactory, ciMapaLayersFactory, NotificadorService, MAPA_CONSTANTS_INCORPORACAO, $state, PontoImovel, $rootScope, $mdDialog, ciMapaStyleFactory, ValidaTerrenoBenfeitoriaService, ImovelPontoService, ImovelOverlayService, $document, $compile, tabsIncorporacao, UnidadeAutonomaService, MensagensService, MapaService, obterTerrenosIntersedidos) {
      return {
          templateUrl: 'scripts-cadastro-imovel/app/directives/ci-mapa/view/ci-mapa-template.html',
          scope: {
              showMinimapa: '=showminimapa',
              showTestada: '=showTestada',
              heightMinimapa: '=heightminimapa',
              widthMinimapa: '=widthminimapa',
              tabIndex: '=tabindex',
              isUnidadeAutonoma: '=isunidadeautonoma',
              imovel: '=',
              action: '@',
              benfeitoria: '=',
              habilitarDesenho: "=?",
              edificacoes: '='
          },
          link: function(scope, element) {
              angular.element(document).ready(function() {
                  var vm = scope,
                      viewFac = ciMapaViewFactory,
                      layersFac = ciMapaLayersFactory,
                      imovelSource = new ol.source.Vector,
                      imovelLayer = new ol.layer.Vector({
                          source: imovelSource,
                          name: 'imovel',
                          required: true
                      }),
                      vectorGeomCadastradosSource = new ol.source.Vector,
                      vectorGeomCadastrados = new ol.layer.Vector({
                          source: vectorGeomCadastradosSource,
                          name: 'vectorGeomCadastrados',
                          required: true
                      }),
                      geoJson = new ol.format.GeoJSON({
                          defaultProjection: 'EPSG:4674'
                      }),
                      drawPoint,
                      drawPointTestada,
                      drawPolygon,
                      setDrawPoint,
                      ID_NATUREZA_IMOVEL_URBANO = 1,
                      ID_NATUREZA_IMOVEL_RURAL = 2,
                      ID_NATUREZA_IMOVEL_INDEFINIDO = 3,
                      styles = ciMapaStyleFactory,
                      notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES,
                      featureIds = MAPA_CONSTANTS_INCORPORACAO.FEATURE_ID,
                      camadas = MAPA_CONSTANTS_INCORPORACAO.LAYERS,
                      tiposImovel = MAPA_CONSTANTS_INCORPORACAO.TIPO_IMOVEL,
                      terrenoIntersecaoStyle,
                      terrenoInvalidoStyle,
                      terrenoAmareloStyle,
                      centroSegmentoStyle,
                      adicionarMarcador,
                      mapClickEvent,
                      terrenoCartorialValidoStyle,
                      setClickEvent,
                      drawPolygonBenfeitoria,
                      setDrawPolygonBenfeitoria,
                      terrenoDefaultStyle,
                      destacarBenfeitoria,
                      imovel = scope.imovel,
                      terrenoImovel,
                      geometriaImovelTerrenoCadastralJson,
                      geometriaImovelTerrenoCartorialJson,
                      geometriaImovelTerrenoCadastral,
                      geometriaImovelTerrenoCartorial,
                      geometriaImovelPontoJson,
                      geometriaImovelPonto,
                      geometriaImovelPontoTestadaJson,
                      geometriaImovelPontoTestada,
                      geometriaImovelUnidadeAutonomaJson,
                      geometriaImovelUnidadeAutonoma,
                      action = scope.action,
                      removeAndSetPonto,
                      removeAndSetPontoTestada,
                      feature = new ol.Feature({}),
                      setDrawPolygonUnidadeAutonoma,
                      drawPolygonUnidadeAutonoma,
                      desenhaPorGeometria,
                      setStyleTerrenoByFeature,
                      getImovelOverlayHTML,
                      getImovelById,
                      pontoImovelValidoStyle,
                      pontoImovelValidoTestadaStyle,
                      pontoImovelInvalidoStyle,
                      terrenoValidoStyle,
                      terrenoValidoPontilhadoStyle,
                      unidadeAutonomaStyle,
                      benfeitoriaStyle,
                      benfeitoriaDestacadaStyle,
                      removeFeatures,
                      adicionaPontoFeatures,
                      removerFeaturesUnidadeAutonoma,
                      getFeatureByUuid,
                      removeFeatureUnidadeAutonomaByNameLayer,
                      desenharGeometriaEdificacao,
                      featuresRemovidasUnidadeAutonomas = [],
                      redesenharFeaturesRemovidasUnidadeAutonoma,
                      adicionaFeatureByLayer,
                      overlay_,
                      ABA_LOCALIZACAO = -1,
                      ABA_TERRENO = 1,
                      ABA_UNIDADE_AUTONOMA = 3,
                      ABA_BENFEITORIA = 4;

                  vm.layers = layersFac.getCamadas(false);
                  vm.map = {};
                  vm.coordenadasXY = {};
                  vm.listaLayers = false;
                  vm.bloquearMapa = true;
                  vm.bloquearMapaTerreno = false;
                  vm.bloquearMapaUnidadeAutonoma = false;
                  vm.bloquearMapaBenfeitoria = false;

                  var vmr = $rootScope;

                  function init() {
                      vm.map = new ol.Map({
                          target: element[0].firstChild,
                          view: viewFac.getView(),
                          controls: ol.control.defaults({
                              attribution: false,
                              rotate: false
                          }).extend([
                              new ol.control.FullScreen({
                                  tipLabel: 'Expandir Mapa',
                                  className: 'btn-fullscreen',
                                  label: '\u2922'
                              }),
                              new ol.control.ScaleLine(),
                              new ol.control.Attribution({
                                  className: 'ol-attribution ol-attribution-custom'
                              }),
                              new ol.control.MousePosition({
                                  coordinateFormat: ol.coordinate.createStringXY(4),
                                  projection: 'EPSG:4326',
                                  className: 'custom-mouse-position',
                                  target: document.getElementById('mouse-position'),
                                  undefinedHTML: '',
                              })
                          ]),
                          interactions: ol.interaction.defaults({
                              altShiftDragRotate: false
                          }),
                          renderer: 'canvas'
                      });
                      angular.forEach(vm.layers, function(layer) {
                          vm.map.addLayer(layer);
                      });

                      vm.map.getLayerGroup().getLayers().insertAt(vm.map.getLayerGroup().getLayers().getArray().length, imovelLayer);
                      vm.map.getLayerGroup().getLayers().insertAt(vm.map.getLayerGroup().getLayers().getArray().length, vectorGeomCadastrados);

                      /* Estilo dos pontos */
                      pontoImovelInvalidoStyle = styles.pinVermelho();

                      if (scope.tabIndex === -1) {
                          pontoImovelValidoStyle = styles.pinAmarelo();
                      } else {
                          pontoImovelValidoStyle = styles.pinVerde();
                          pontoImovelValidoTestadaStyle = styles.pinVerdeTestada();
                      }

                      /* Estilo dos terrenos */
                      terrenoInvalidoStyle = styles.terrenoVermelho();
                      terrenoIntersecaoStyle = styles.terrenoRoxo();

                      if (scope.tabIndex === 1) {
                          terrenoValidoStyle = styles.terrenoAmarelo();
                          terrenoValidoPontilhadoStyle = styles.terrenoAmareloPontilhado();
                      } else {
                          terrenoValidoStyle = styles.terrenoVerde();
                          terrenoValidoPontilhadoStyle = styles.terrenoVerdePontilhado();
                      }

                      /* Estilo da unidade autônoma*/
                      unidadeAutonomaStyle = styles.terrenoVerde();
                      /*if (scope.tabIndex === 3) {
                          unidadeAutonomaStyle = styles.terrenoAmarelo();
                      } else {
                          unidadeAutonomaStyle = styles.terrenoVerde();
                      }*/

                      /* Estilo da benfeitoria */
                      benfeitoriaDestacadaStyle = styles.terrenoAmarelo();
                      if (scope.tabIndex === 4) {
                          benfeitoriaStyle = styles.terrenoAmarelo();
                      } else {
                          benfeitoriaStyle = styles.terrenoVerde();
                      }

                      vm.map.on("pointermove", function(event) {
                          var pixel = vm.map.getEventPixel(event.originalEvent);
                          var hit = vm.map.forEachFeatureAtPixel(pixel, function(feature, layer) {
                              if (feature.get('name') == featureIds.CENTRO_SEGMENTO || feature.getId() == featureIds.PONTO_IMOVEL_TESTADA) {
                                  return true;
                              }
                          });

                          if (hit) {
                              vm.map.getTarget().style.cursor = 'pointer';
                          } else {
                              vm.map.getTarget().style.cursor = '';
                          }
                      });

                      vm.map.getView().on("change:resolution", function() {
                        if (vm.map.getView().getZoom() <= 13) {
                              removerFeaturesByName(featureIds.CENTRO_SEGMENTO);
                          } else if (scope.tabIndex === 1) {
                              adicionarCentroSegmentos();
                          }
                      });

                      if (scope.tabIndex === -1) {
                          overlay_ = new ol.Overlay({
                              element: angular.element(document.querySelector('#overlayLocalizacao'))[0],
                              positioning: 'bottom-left'
                          });
                          vm.map.addOverlay(overlay_);
                      }

                      if (scope.tabIndex === 0) {
                          overlay_ = new ol.Overlay({
                              element: angular.element(document.querySelector('#overlayDadosGerais'))[0],
                              positioning: 'bottom-left'
                          });
                          vm.map.addOverlay(overlay_);
                      }

                      if (scope.tabIndex === 1) {
                          var divBloqueioTelaTerreno =
                              $compile("<div id=\"bloqueioTelaTerreno\" ng-if=\"bloquearMapaTerreno\" style=\"height: 100%; width: 100%; position: absolute; z-index: 1; background-color: rgba(0,0,0,0.7); top: 0; left: 0;\">" +
                                  "<div style=\"display: inline-block; background-color: #ecffe6; border-radius: 20px; padding-right: 5px; color: #4caf50; position: absolute; top: 276px; left: 26px;\">" +
                                  "<md-button style=\"margin: 0;\" class=\"mapa-botoes-icone md-icon-button\" ng-click=\"desbloquearTelaTerreno();\">" +
                                  "<ng-md-icon icon=\"edit\" style=\"fill: #fff;\">" +
                                  "<md-tooltip>Desenhar Terreno</md-tooltip>" +
                                  "</ng-md-icon>" +
                                  "</md-button>" +
                                  "Selecione aqui desenhar o terreno" +
                                  "</div>" +
                                  "</div>")(vm);

                          angular.element($document.find("md-content")[0]).append(divBloqueioTelaTerreno);

                          overlay_ = new ol.Overlay({
                              element: angular.element(document.querySelector('#overlayTerreno'))[0],
                              positioning: 'bottom-left'
                          });
                          vm.map.addOverlay(overlay_);
                      }

                      if (scope.tabIndex === 2) {
                          overlay_ = new ol.Overlay({
                              element: angular.element(document.querySelector('#overlayTabBenfeitoria'))[0],
                              positioning: 'bottom-left'
                          });
                          vm.map.addOverlay(overlay_);
                      }

                      if (scope.tabIndex === 3) {
                          var divBloqueioTelaUnidadeAutonoma =
                              $compile("<div id=\"bloqueioTelaUnidadeAutonoma\"" +
                                  " ng-if=\"bloquearMapaUnidadeAutonoma\"" +
                                  " style=\"height:" +
                                  " 100%;" +
                                  " width: 100%; position: absolute; z-index: 1; background-color:" +
                                  " rgba(0,0,0,0.7); top: 0; left: 0;\">" +
                                  "<div style=\"display: inline-block; background-color: #ecffe6; border-radius: 20px; padding-right: 5px; color: #4caf50; position: absolute; top: 276px; left:" +
                                  " 26px;\">" +
                                  "<md-button style=\"margin: 0;\" class=\"mapa-botoes-icone md-icon-button\" ng-click=\"desbloquearTelaUnidadeAutonoma();\">" +
                                  "<ng-md-icon icon=\"edit\" style=\"fill: #fff;\">" +
                                  "<md-tooltip>Desenhar Unidade Autônoma</md-tooltip>" +
                                  "</ng-md-icon>" +
                                  "</md-button>" +
                                  "Selecione aqui desenhar a unidade autônoma" +
                                  "</div>" +
                                  "</div>")(vm);

                          angular.element($document.find("md-content")[0]).append(divBloqueioTelaUnidadeAutonoma);
                          overlay_ = new ol.Overlay({
                              element: angular.element(document.querySelector('#overlayUnidadeAutonoma'))[0],
                              positioning: 'bottom-left'
                          });
                          vm.map.addOverlay(overlay_);

                      }

                      if (scope.tabIndex === 4) {
                          var divBloqueioTelaBenfeitoria =
                              $compile("<div id=\"bloqueioTelaBenfeitoria\" ng-if=\"bloquearMapaBenfeitoria\" style=\"height: 100%; width: 100%; position: absolute; z-index: 1; background-color: rgba(0,0,0,0.7); top: 0; left: 0;\">" +
                                  "<div style=\"display: inline-block; background-color: #ecffe6; border-radius: 20px; padding-right: 5px; color: #4caf50; position: absolute; top: 260px;left: 36px;\">" +
                                  "<md-button style=\"margin: 0;\" class=\"mapa-botoes-icone md-icon-button\" ng-click=\"desbloquearTelaBenfeitoria();\">" +
                                  "<ng-md-icon icon=\"edit\" style=\"fill: #fff;\">" +
                                  "<md-tooltip>Desenhar Benfeitoria</md-tooltip>" +
                                  "</ng-md-icon>" +
                                  "</md-button>" +
                                  "Selecione aqui desenhar a benfeitoria" +
                                  "</div>" +
                                  "</div>")(vm);

                          angular.element($document.find("md-content")[0]).append(divBloqueioTelaBenfeitoria);

                          overlay_ = new ol.Overlay({
                              element: angular.element(document.querySelector('#overlayInclusaoBenfeitoria'))[0],
                              positioning: 'bottom-left'
                          });
                          vm.map.addOverlay(overlay_);
                      }

                      if (scope.tabIndex === 5) {
                          overlay_ = new ol.Overlay({
                              element: angular.element(document.querySelector('#overlayProprietario'))[0],
                              positioning: 'bottom-left'
                          });
                          vm.map.addOverlay(overlay_);
                      }

                      if (scope.action === 'pesquisarImovel' || scope.action === 'detalharImoveisHomologar') {
                          overlay_ = new ol.Overlay({
                              element: angular.element(document.querySelector('#overlayPesquisarImovel'))[0],
                              positioning: 'bottom-left'
                          });
                          vm.map.addOverlay(overlay_);
                      }

                      if (scope.action === 'verlocalizacao') {
                          overlay_ = new ol.Overlay({
                              element: angular.element(document.querySelector('#overlayVerLocalizacao'))[0],
                              positioning: 'bottom-left'
                          });
                          vm.map.addOverlay(overlay_);
                      }

                      vm.map.on('moveend', function(evt) {
                          if (viewFac.getView().getZoom() >= 13)
                              onMoveEnd(evt);
                      });

                      vm.map.on('click', function(evt) {
                          var feature,
                              elemento,
                              elementoHTML, features;

                          feature = vm.map.forEachFeatureAtPixel(evt.pixel, function(feature) {
                              return feature;
                          });

                          elemento = overlay_.getElement();
                          if (feature) {
                              if (feature.get('features')) {
                                  features = feature.get('features');
                                  feature = features[features.length - 1];
                              }
                              if (feature.get('uuidTerreno')) {
                                  ImovelOverlayService.query({ uuid: feature.get('uuidTerreno') }).$promise.then(function(data) {
                                      elemento.innerHTML = "";
                                      vm.imoveisOverlay = data;
                                      elementoHTML = $compile(getImovelOverlayHTML())(vm);
                                      angular.element(elemento).append(elementoHTML);
                                      overlay_.setPosition(evt.coordinate);
                                  });
                              }
                              if (feature.getId(featureIds.PONTO_IMOVEL_TESTADA) && vm.action === 'edit') {
                                  scope.imovel.pontoImovelTestada = undefined;
                                  apagarFeature(featureIds.PONTO_IMOVEL_TESTADA);
                              }
                          } else {
                              overlay_.setPosition(undefined);
                              elemento.innerHTML = "";
                          }
                      });

                      if (scope.imovel) {
                          geometriaImovelPontoJson = scope.imovel.pontoImovel;
                          geometriaImovelPonto = geoJson.readGeometry(geometriaImovelPontoJson);
                          geometriaImovelPontoTestadaJson = scope.imovel.pontoImovelTestada;
                          geometriaImovelPontoTestada = geoJson.readGeometry(geometriaImovelPontoTestadaJson);
                          terrenoImovel = scope.imovel.terreno;
                          if (terrenoImovel) {
                              geometriaImovelTerrenoCadastralJson = terrenoImovel.geometriaTerreno;
                              geometriaImovelTerrenoCartorialJson = terrenoImovel.geometriaTerrenoCartorial;
                              geometriaImovelTerrenoCadastral = geoJson.readGeometry(geometriaImovelTerrenoCadastralJson);
                              geometriaImovelTerrenoCartorial = geoJson.readGeometry(geometriaImovelTerrenoCartorialJson);

                          }

                          if (scope.imovel.terreno.geometriaTerreno) {
                              feature = new ol.Feature({
                                  geometry: geometriaImovelTerrenoCadastral
                              });
                              feature.setId(featureIds.TERRENO_CADASTRAL);
                              if (scope.imovel.terreno.geometriaTerrenoCartorial) {
                                  feature.setStyle(terrenoValidoPontilhadoStyle);
                              } else {
                                  feature.setStyle(terrenoValidoStyle);
                              }
                              imovelSource.addFeature(feature);
                              viewFac.centralizarMapa(feature.getGeometry().getExtent(), vm.map.getSize());
                          }

                          if (scope.imovel.terreno.geometriaTerrenoCartorial) {
                              feature = new ol.Feature({
                                  geometry: geometriaImovelTerrenoCartorial
                              });
                              feature.setId(featureIds.TERRENO_CARTORIAL);
                              feature.setStyle(terrenoValidoStyle);
                              imovelSource.addFeature(feature);
                          }

                          if (scope.imovel.pontoImovel) {
                              feature = new ol.Feature({
                                  geometry: geometriaImovelPonto
                              });
                              feature.setId(featureIds.PONTO_IMOVEL);
                              feature.setStyle(pontoImovelValidoStyle);
                              imovelSource.addFeature(feature);
                              vm.getImoveisRaio2000Km(geometriaImovelPonto);
                          }

                          if (scope.imovel.pontoImovelTestada) {
                              feature = new ol.Feature({
                                  geometry: geometriaImovelPontoTestada
                              });
                              feature.setId(featureIds.PONTO_IMOVEL_TESTADA);
                              feature.setStyle(pontoImovelValidoTestadaStyle);
                              imovelSource.addFeature(feature);
                          }

                          if (scope.imovel.unidadeAutonoma && scope.imovel.unidadeAutonoma.edificacao && scope.imovel.unidadeAutonoma.edificacao.geometria) {
                              geometriaImovelUnidadeAutonomaJson = scope.imovel.unidadeAutonoma.edificacao.geometria;
                              geometriaImovelUnidadeAutonoma = geoJson.readGeometry(geometriaImovelUnidadeAutonomaJson);
                              feature = new ol.Feature({
                                  geometry: geometriaImovelUnidadeAutonoma
                              });
                              feature.setId(featureIds.UNIDADE_AUTONOMA);
                              feature.setStyle(unidadeAutonomaStyle);
                              imovelSource.addFeature(feature);
                          }

                          if (scope.imovel.benfeitorias && scope.imovel.benfeitorias.length > 0) {
                              angular.forEach(scope.imovel.benfeitorias, function(benfeitoria) {

                                if (benfeitoria.ativa != false) {
                                   var feature;
                                  feature = new ol.Feature({
                                      geometry: geoJson.readGeometry(benfeitoria.geometriaBenfeitoria)
                                  });
                                  feature.setId(benfeitoria.codigo);
                                  feature.setStyle(benfeitoriaStyle);
                                  feature.set('name', 'benfeitoria');
                                  imovelSource.addFeature(feature);
                                }
                              });
                          }
                      }
                  }

                  // Estilos de Features

                  terrenoAmareloStyle = new ol.style.Style({
                      fill: new ol.style.Fill({
                          color: 'rgba(255, 206, 0, 0.3)'
                      }),
                      stroke: new ol.style.Stroke({
                          color: 'rgba(255, 206, 0, 1)',
                          width: 3
                      }),
                      zIndex: 1
                  });

                  terrenoCartorialValidoStyle = new ol.style.Style({
                      fill: new ol.style.Fill({
                          color: 'rgba(30, 144, 255, 0.2)'
                      }),
                      stroke: new ol.style.Stroke({
                          width: 3,
                          color: 'rgba(30, 144, 255, 1)',
                          lineDash: [0.1, 5]
                      }),
                      zIndex: 2
                  });

                  terrenoDefaultStyle = new ol.style.Style({
                      fill: new ol.style.Fill({
                          color: 'rgba(107, 181, 255, 0.15)'
                      }),
                      stroke: new ol.style.Stroke({
                          color: 'rgb(0, 125, 250)',
                          width: 1
                      }),
                      zIndex: 4
                  });

                  centroSegmentoStyle = new ol.style.Style({
                      image: new ol.style.Circle({
                          radius: 3,
                          fill: new ol.style.Fill({
                              color: '#0000ff'
                          })
                      }),
                      zIndex: 5
                  });
                  // Fim - Estilos de Features

                  if (scope.tabIndex === -1 && scope.action === 'add') {
                      var controle;
                      //
                      vm.$watch('imovel.tipoImovel', function(newTipoImovel, oldTipoImovel) {
                          if (newTipoImovel && hasEdificacao() && controle != newTipoImovel) {
                              if ((isLoteComEdificacao(oldTipoImovel) || isEspelhoDAgua(oldTipoImovel)) &&
                                  (isUnidadeAutonoma(newTipoImovel) || isCavidadeNatural(newTipoImovel) || isLoteSemEdificacao(newTipoImovel))) {
                                  var confirm = $mdDialog.confirm({
                                      focusOnOpen: false,
                                      textContent: "Caso altere o tipo de imóvel as informações da(s) benfeitoria(s) serão perdidas. Deseja continuar?",
                                      ok: "Sim",
                                      cancel: "Não"
                                  });

                                  $mdDialog.show(confirm).then(function() {
                                      NotificadorService.notify(scope, null, notifiers.REMOVER_EDIFICACOES);
                                      excluiGeometriaEdificacoes();
                                      excluiBenfeitorias();
                                  }, function() {
                                      controle = oldTipoImovel;
                                      vm.imovel.tipoImovel = oldTipoImovel;
                                      tabsIncorporacao.atualizaTabs(vm.imovel.tipoImovel);
                                  });
                              } else if (!isUnidadeAutonoma(newTipoImovel) && isUnidadeAutonoma(oldTipoImovel)) {
                                  var dialog = $mdDialog.confirm({
                                      focusOnOpen: false,
                                      textContent: "Caso altere o tipo de imóvel as informações da Unidade Autônoma serão perdidas. Deseja continuar?",
                                      ok: "Sim",
                                      cancel: "Não"
                                  });

                                  $mdDialog.show(dialog).then(function() {
                                      NotificadorService.notify(scope, null, notifiers.REMOVER_EDIFICACOES);
                                      excluiGeometriaEdificacoes();
                                      scope.imovel.unidadeAutonoma = UnidadeAutonomaService.novaUnidadeAutonoma();
                                  }, function() {
                                      controle = oldTipoImovel;
                                      vm.imovel.tipoImovel = oldTipoImovel;
                                      tabsIncorporacao.atualizaTabs(vm.imovel.tipoImovel);
                                  });
                              }
                          }
                      });
                  }

                  function excluiBenfeitorias() {
                      scope.imovel.benfeitorias = [];
                  }

                  function isUnidadeAutonoma(tipoImovel) {
                      return tipoImovel && tipoImovel.tipoPai === "UNIDADE AUTÔNOMA";
                  }

                  function isCavidadeNatural(tipoImovel) {
                      return tipoImovel && tipoImovel.id === 5;
                  }

                  function isLoteSemEdificacao(tipoImovel) {
                      return tipoImovel && tipoImovel.id === 1;
                  }

                  function isLoteComEdificacao(tipoImovel) {
                      return tipoImovel && tipoImovel.id === 2;
                  }

                  function isEspelhoDAgua(tipoImovel) {
                      return tipoImovel && tipoImovel.id === 3;
                  }

                  vm.$on('broadVerificaMapaBing', function(event, args) {
                      $rootScope.$broadcast('retornoVerificaMapaBing', { any: vm.map });
                  });

                  function hasEdificacao() {
                      var features = imovelSource.getFeatures();
                      for (var i = 0; i < features.length; i++) {
                          if (isFeatureEdificacao(features[i])) {
                              return true;
                          }
                      }
                      return false;
                  }

                  function excluiGeometriaEdificacoes() {
                      angular.forEach(imovelSource.getFeatures(), function(feature) {
                          if (isFeatureEdificacao(feature)) {
                              imovelSource.removeFeature(feature);
                          }
                      });
                  }

                  function isFeatureEdificacao(feature) {
                      var patt = new RegExp("^E[0-9]+$");
                      return feature.getId() && (feature.getId().includes(featureIds.ACESSAO_BENFEITORIA) || feature.getId().includes(featureIds.UNIDADE_AUTONOMA) || patt.test(feature.getId()));
                  }

                  if (scope.tabIndex === -1) {
                      NotificadorService.subscribe(scope, function(event, scope, geometriaJson) {
                          var geometria;
                          if (geometriaJson) {
                              geometria = geoJson.readGeometry(geometriaJson);
                              viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());
                          }
                      }, notifiers.CENTRALIZAR_POR_MUNICIPIO);

                      NotificadorService.subscribe(scope, function(event, escopo, dado) {

                          removerFeatureById(featureIds.PONTO_IMOVEL);
                          removerFeatureById(featureIds.TERRENO_CADASTRAL);
                          removerFeatureById(featureIds.TERRENO_CARTORIAL);
                          angular.forEach(scope.edificacoes, function(unidadeAutonoma) {
                              removerFeatureById(unidadeAutonoma.edificacao.uuid);
                          });

                          NotificadorService.notify(scope, false, notifiers.FEATURE_EXCLUIDA_UNIDADE_AUTONOMA);
                          redesenharFeaturesRemovidasUnidadeAutonoma();
                          NotificadorService.notify(scope, false, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                          NotificadorService.notify(scope, false, notifiers.PONTO_IN_UNIDADE_AUTONOMA);
                      }, notifiers.REMOVER_PONTO_EDIFICACOES_TERRENO_UA);

                      NotificadorService.subscribe(scope, function(event, escopo, geometriaJson) {
                          var geometria = geoJson.readGeometry(geometriaJson);
                          var feature = new ol.Feature({
                              geometry: geometria
                          });

                          inserirPonto(feature);
                          viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());
                      }, notifiers.MARCAR_PONTO_POR_COORDENADAS);
                  }
                  if (scope.tabIndex === 1) {
                      NotificadorService.subscribe(scope, function(evento, escopo, data) {
                          if (data) {
                              var featureTerreno = new ol.Feature({
                                  geometry: geoJson.readGeometry(data.feature)
                              });
                              validarGeometriaTerreno(featureTerreno, data.tipoTerreno, "uploadShapefile");
                              viewFac.centralizarMapa(featureTerreno.getGeometry().getExtent(), vm.map.getSize());
                          }
                      }, notifiers.UPLOAD_SHAPEFILE);

                      NotificadorService.subscribe(scope, function() {
                          vm.bloquearMapaTerreno = true;
                      }, "bloquearTelaTerreno");
                  }
                  if (scope.tabIndex === 2) {

                      NotificadorService.subscribe(scope, function(evento, escopo, codigo) {
                          destacarBenfeitoria(codigo);
                      }, notifiers.DESTACAR_BENFEITORIA);

                      NotificadorService.subscribe(scope, function(evento, escopo, codigo) {
                          removerFeatureById(codigo);
                          MapaService.removeFeature(scope, codigo);
                      }, notifiers.BENFEITORIA_EXCLUIDA);
                  }
                  if (scope.tabIndex === 3) {
                      NotificadorService.subscribe(scope, function(evento, escopo, geometria) {
                          var id = scope.imovel.unidadeAutonoma.edificacao.uuid || featureIds.UNIDADE_AUTONOMA;
                          removerFeatureById(id);

                          var feature = new ol.Feature({
                              geometry: geoJson.readGeometry(geometria)
                          });
                          feature.setStyle(unidadeAutonomaStyle);
                          feature.setId(id);
                          feature.set('tipoDesenho', 'uploadShapefile');
                          imovelSource.addFeature(feature);

                          viewFac.centralizarMapa(feature.getGeometry().getExtent(), vm.map.getSize());

                          NotificadorService.notify(scope, feature, notifiers.ADICIONAR_FEATURE_UNIDADE_AUTONOMA);
                      }, notifiers.UPLOAD_SHAPEFILE_UNIDADE_AUTONOMA);

                      NotificadorService.subscribe(scope, function() {
                          vm.bloquearMapaUnidadeAutonoma = true;
                      }, "bloquearTelaUnidadeAutonoma");
                  }
                  if (scope.tabIndex === 4) {
                      NotificadorService.subscribe(scope, function(evento, escopo, obj) {

                          removerFeatureById(obj.id);

                          var feature = new ol.Feature({
                              geometry: geoJson.readGeometry(obj.geometria)
                          });

                          feature.setStyle(benfeitoriaStyle);
                          feature.setId(obj.id);
                          feature.set('name', 'benfeitoria');
                          feature.set('tipoDesenho', 'uploadShapefile');
                          imovelSource.addFeature(feature);

                          viewFac.centralizarMapa(feature.getGeometry().getExtent(), vm.map.getSize());

                          NotificadorService.notify(scope, feature, notifiers.ADICIONAR_FEATURE_BENFEITORIA);
                      }, notifiers.UPLOAD_SHAPEFILE_BENFEITORIA);

                      NotificadorService.subscribe(scope, function() {
                          vm.bloquearMapaBenfeitoria = true;
                      }, "bloquearTelaBenfeitoria");
                  }

                  NotificadorService.subscribe(scope, function(evento, escopo, obj) {
                      var feature;

                      if (obj.codigo && obj.geometriaBenfeitoria) {
                          removerFeatureById(obj.codigo);
                          feature = new ol.Feature({
                              geometry: geoJson.readGeometry(obj.geometriaBenfeitoria)
                          });
                          feature.setId(obj.codigo);
                          feature.setStyle(benfeitoriaStyle);
                          feature.set('name', 'benfeitoria');
                          imovelSource.addFeature(feature);
                      } else if (obj.codigo) {
                          removerFeatureById(obj.codigo);
                      } else {
                          removerFeatureById(featureIds.ACESSAO_BENFEITORIA);
                      }
                  }, notifiers.DESFAZER_ALTERACAO_BENFEITORIA);

                  NotificadorService.subscribe(scope, function(evento, escopo, tipoTerreno) {
                      if (scope.$id != escopo.$id) {
                          removerFeatureById(featureIds.PONTO_IMOVEL);
                          removerFeatureById(featureIds.TERRENO_CADASTRAL);
                          removerFeatureById(featureIds.TERRENO_CARTORIAL);
                          angular.forEach(scope.edificacoes, function(unidadeAutonoma) {
                              removerFeatureById(unidadeAutonoma.edificacao.uuid);
                          });

                          if (scope.tabIndex === 1 && scope.action == 'add') {
                              removerFeaturesByName(featureIds.CENTRO_SEGMENTO);
                              NotificadorService.notify(scope, true, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                          }
                      }
                  }, notifiers.FEATURE_EXCLUIDA_UNIDADE_AUTONOMA);

                  NotificadorService.subscribe(scope, function(event, scope, obj) {
                      var feature = imovelSource.getFeatureById(obj.oldValue);
                      feature.setId(obj.newValue);
                  }, notifiers.SET_ID_BENFEITORIA);

                  NotificadorService.subscribe(scope, function(event, scope, codBenfeitoria) {
                    removerCustomInteractions();
                    var id = codBenfeitoria || featureIds.ACESSAO_BENFEITORIA;
                    removerFeatureById(id);
                    //NotificadorService.notify(vm, { geoBenfeitoria: undefined, isValid: false }, notifiers.GEOMETRIA_BENFEITORIA_ALTERADA);
                    vmr.geometriaBenfeitoria = undefined;
                  }, notifiers.EXCLUI_BENFEITORIA_BY_ID);

                  NotificadorService.subscribe(scope, function(evento, escopo, tipoTerreno) {
                      removerFeatureById(tipoTerreno);
                      removerFeatureTerrenoSobreposto(tipoTerreno);
                      adicionarCentroSegmentos();
                      redesenharFeaturesRemovidasUnidadeAutonoma();
                  }, notifiers.LIMPAR_DESENHO);

                  NotificadorService.subscribe(scope, function(evento, escopo, obj) {
                      var feature = obj.feature.clone();
                      feature.setId(featureIds.PONTO_IMOVEL);
                      removerFeatureById(featureIds.PONTO_IMOVEL);
                      if (obj.isValido) {
                          feature.setStyle(pontoImovelValidoStyle);
                      } else {
                          feature.setStyle(pontoImovelInvalidoStyle);
                      }
                      imovelSource.addFeature(feature);
                      // vm.getImoveisRaio2000Km(feature.getGeometry());
                  }, notifiers.ADICIONAR_FEATURE_PONTO);

                  NotificadorService.subscribe(scope, function(evento, escopo, obj) {
                    if (obj.feature != undefined){
                        var feature = obj.feature.clone();
                        feature.setId(featureIds.PONTO_IMOVEL_TESTADA);
                        removerFeatureById(featureIds.PONTO_IMOVEL_TESTADA);
                        if (obj.isValido) {
                            feature.setStyle(pontoImovelValidoTestadaStyle);
                        } else {
                            feature.setStyle(pontoImovelInvalidoStyle);
                        }
                        imovelSource.addFeature(feature);
                    }
                  }, notifiers.ADICIONAR_FEATURE_PONTO_TESTADA);

                  NotificadorService.subscribe(scope, function(evento, escopo, obj) {
                      if (scope.$id != escopo.$id) {
                          var featureTerreno = obj.feature.clone(),
                              tipoTerreno = obj.feature.getId();

                          removerFeatureById(tipoTerreno);

                          removerFeatureTerrenoSobreposto(tipoTerreno);

                          if (obj.intersecoes && obj.intersecoes.length > 0) {
                              adicionarFeatureTerrenoSobreposto(obj.intersecoes, tipoTerreno);
                          }

                          if (obj.isValid) {
                              setStyleTerrenoByFeature(featureTerreno, tipoTerreno);
                          } else {
                              featureTerreno.setStyle(terrenoInvalidoStyle);
                          }
                          featureTerreno.setId(tipoTerreno);
                          imovelSource.addFeature(featureTerreno);
                      }
                  }, notifiers.ADICIONAR_FEATURE_TERRENO);

                  NotificadorService.subscribe(scope, function(evento, escopo, feature) {
                      if (scope.$id != escopo.$id) {
                          var featureBenfeitoria = feature.clone(),
                              id = feature.getId();

                          removerFeatureById(id);
                          featureBenfeitoria.setStyle(benfeitoriaStyle);
                          featureBenfeitoria.setId(id);
                          imovelSource.addFeature(featureBenfeitoria);
                      }
                  }, notifiers.ADICIONAR_FEATURE_BENFEITORIA);

                  NotificadorService.subscribe(scope, function(evento, escopo, feature) {
                      if (scope.$id != escopo.$id) {
                          var featureUA = feature.clone();
                          var id = feature.getId() || featureIds.UNIDADE_AUTONOMA;

                          removerFeatureById(id);
                          featureUA.setId(id);
                          featureUA.setStyle(unidadeAutonomaStyle);
                          imovelSource.addFeature(featureUA);

                          if (scope.tabIndex === 1 && scope.action == 'add') {
                              adicionarCentroSegmentos();
                              setClickEvent();
                              NotificadorService.notify(scope, true, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                          }

                      }
                  }, notifiers.ADICIONAR_FEATURE_UNIDADE_AUTONOMA);

                  NotificadorService.subscribe(scope, function(evento, escopo, dado) {
                      if (scope.$id != escopo.$id) {
                          excluiGeometriaEdificacoes();
                      }
                  }, notifiers.REMOVER_EDIFICACOES);

                  NotificadorService.subscribe(scope, function(evento, escopo, resultadoPesquisarImovel) {
                      var height = vm.map.getTarget().clientHeight;
                      var width = vm.map.getTarget().clientWidth;
                      vm.map.setSize([width, height]);
                      removeFeatures();
                      scope.resultadoPesquisarImovel = resultadoPesquisarImovel;
                      if (resultadoPesquisarImovel.length && resultadoPesquisarImovel.length > 0) {
                          var pontoImovel,
                              geometriaTerrenoCadastral,
                              geometriaTerrenoCartorial,
                              unidadeAutonoma,
                              benfeitorias,
                              pontoFeature,
                              geometriaTerrenoCadastralFeature,
                              geometriaTerrenoCartorialFeature,
                              unidadeAutonomaFeature,
                              benfeitoriaFeature;

                          angular.forEach(resultadoPesquisarImovel, function(imovel) {

                              pontoImovel = imovel.pontoImovel;
                              geometriaTerrenoCadastral = imovel.terreno.geometriaTerreno;
                              geometriaTerrenoCartorial = imovel.terreno.geometriaTerrenoCartorial;
                              unidadeAutonoma = imovel.unidadeAutonoma;
                              benfeitorias = imovel.benfeitorias;

                              if (geometriaTerrenoCadastral) {
                                  geometriaTerrenoCadastralFeature = new ol.Feature({ geometry: new ol.geom.Polygon(geometriaTerrenoCadastral.coordinates) });
                                  geometriaTerrenoCadastralFeature.setId(imovel.id + "terrenoCadastral");
                                  geometriaTerrenoCadastralFeature.setStyle(terrenoAmareloStyle);
                                  imovelSource.addFeature(geometriaTerrenoCadastralFeature);
                              }

                              if (geometriaTerrenoCartorial) {
                                  geometriaTerrenoCartorialFeature = new ol.Feature({ geometry: new ol.geom.Polygon(geometriaTerrenoCartorial.coordinates) });
                                  geometriaTerrenoCartorialFeature.setId(imovel.id + "terrenoCartorial");
                                  geometriaTerrenoCartorialFeature.setStyle(terrenoCartorialValidoStyle);
                                  imovelSource.addFeature(geometriaTerrenoCartorialFeature);
                              }

                              if (unidadeAutonoma && unidadeAutonoma.geometriaUnidadeAutonoma) {
                                  unidadeAutonomaFeature = new ol.Feature({ geometry: new ol.geom.Polygon(unidadeAutonoma.geometriaUnidadeAutonoma.coordinates) });
                                  unidadeAutonomaFeature.setId(imovel.id + "unidadeAutonoma");
                                  unidadeAutonomaFeature.setStyle(terrenoDefaultStyle);
                                  imovelSource.addFeature(unidadeAutonomaFeature);
                              }

                              if (benfeitorias && benfeitorias.length > 0) {
                                  angular.forEach(benfeitorias, function(benfeitoria) {
                                      if (benfeitoria.tipoBenfeitoria.id === 1) {
                                          benfeitoriaFeature = new ol.Feature({
                                              geometry: new ol.geom.Polygon(benfeitoria.geometriaBenfeitoria.coordinates)
                                          });
                                          benfeitoriaFeature.setId(imovel.id + "benfeitoria");
                                          benfeitoriaFeature.setStyle(benfeitoriaDestacadaStyle);
                                          imovelSource.addFeature(benfeitoriaFeature);
                                      }
                                  });
                              }

                              if (pontoImovel) {
                                  pontoFeature = new ol.Feature({
                                      geometry: new ol.geom.Point(pontoImovel.coordinates)
                                  });
                                  pontoFeature.setId(imovel.id + "pontoImovel");
                                  pontoFeature.setStyle(pontoImovelValidoStyle);
                                  adicionaPontoFeatures(pontoFeature);
                              }
                          });

                          vm.map.on('click', function(evento) {
                              var feature = vm.map.forEachFeatureAtPixel(evento.pixel, function(feature) {
                                      return feature;
                                  }),
                                  id,
                                  imovelDetalhadoMapa,
                                  elementoHTML,
                                  elemento,
                                  overlay, features, f;

                              overlay = new ol.Overlay({ stopEvent: false });

                              if (feature) {
                                  if (feature.get('features')) {
                                      features = feature.get('features');
                                      f = features[features.length - 1];
                                      id = parseInt(f.getId(), 10);
                                  } else if (feature.getId()) {
                                      id = parseInt(feature.getId(), 10);
                                  }

                                  imovelDetalhadoMapa = getImovelById(id);
                                  scope.imovelSelecionado = imovelDetalhadoMapa;
                                  elementoHTML = getImovelOverlayHTML(imovelDetalhadoMapa);
                                  elemento = angular.element(document.querySelector('#overlayPesquisarImovel'))[0];
                                  elemento.innerHTML = elementoHTML;
                                  overlay.setElement(elemento);
                                  vm.map.addOverlay(overlay);
                                  overlay.setPosition(evento.coordinate);
                                  elemento.setAttribute("style", "display:inline; height:90px;");
                              } else {
                                  overlay.setPosition(undefined);
                              }
                          });
                      }

                  }, notifiers.RESULTADOPESQUISARIMOVEL);

                  NotificadorService.subscribe(scope, function(evento, escopo, id) {
                      if (scope.$id !== escopo.$id) {
                          var feature = imovelSource.getFeatureById(id);

                          if (feature) {
                              imovelSource.removeFeature(feature);
                          }
                      }
                  }, notifiers.REMOVER_FEATURE_BY_ID);

                  NotificadorService.subscribe(scope, function(evento, escopo, tabIndex) {
                    if (tabIndex == vm.tabIndex){
                      vm.apagar();
                    }
                  }, notifiers.LIMPAR_ABA);

                  setClickEvent = function() {
                      vm.map.unByKey(mapClickEvent);
                      mapClickEvent = vm.map.on('click', function(e) {
                          var feature = vm.map.forEachFeatureAtPixel(e.pixel, function(feature) {
                              return feature;
                          });

                          if ((!feature || feature.getId() !== featureIds.PONTO_IMOVEL_TESTADA)) {
                              /*if (vmr.geometriaMun != undefined) {
                                  new ol.geom.Point(e.coordinate);

                                  var geoJson = {};

                                  if (ciMapaValidacaoFactory.validaCepPontoMunicipio(vmr.geometriaMun, e.coordinate)) {
                                      adicionarMarcador(e.coordinate);
                                      NotificadorService.notify(scope, true, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                                  } else {
                                      if (!isEspelhoDAgua(scope.imovel.tipoImovel)) {
                                            MensagensService.exibirMensagemErro("O ponto do imóvel não se encontra sobre o município informado em relação ao CEP.");
                                        } else {
                                          adicionarMarcador(e.coordinate);
                                          NotificadorService.notify(scope, true, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                                        }
                                  }
                              } else {*/
                                  adicionarMarcador(e.coordinate);
                                  NotificadorService.notify(scope, true, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                              //}
                          }
                      });
                  };

                  setDrawPoint = function() {
                      drawPoint = new ol.interaction.Draw({
                          source: imovelSource,
                          type: 'Point',
                          style: pontoImovelValidoStyle
                      });
                  };

                  function setDrawPolygon() {
                      drawPolygon = new ol.interaction.Draw({
                          source: imovelSource,
                          type: 'Polygon'
                      });
                  }

                  setDrawPolygonBenfeitoria = function() {
                      drawPolygonBenfeitoria = new ol.interaction.Draw({
                          source: imovelSource,
                          type: 'Polygon'
                      });
                  };

                  setDrawPolygonUnidadeAutonoma = function() {
                      drawPolygonUnidadeAutonoma = new ol.interaction.Draw({
                          source: imovelSource,
                          type: 'Polygon'
                      });
                  };

                  function setFeatureSobreposicao(geometria, id) {
                      var featureSobreposicao = new ol.Feature({
                          name: id,
                          geometry: geometria
                      });
                      featureSobreposicao.setStyle(terrenoIntersecaoStyle);
                      imovelSource.addFeature(featureSobreposicao);
                  }

                  function removerCustomInteractions() {
                      vm.map.removeInteraction(drawPoint);
                      vm.map.removeInteraction(drawPointTestada);
                      vm.map.removeInteraction(drawPolygon);
                      vm.map.removeInteraction(drawPolygonBenfeitoria);
                      vm.map.removeInteraction(drawPolygonUnidadeAutonoma);
                  }

                  adicionarMarcador = function(coordinate) {
                      var feature = imovelSource.getClosestFeatureToCoordinate(coordinate, function(feature) {
                              return feature.get('name') === featureIds.CENTRO_SEGMENTO;
                          }),
                          pontoFeature;
                      if (feature) {
                          pontoFeature = new ol.Feature({
                              geometry: feature.getGeometry()
                          });
                          validarPonto(pontoFeature);
                          NotificadorService.notify(scope, feature.getGeometry().getCoordinates(), notifiers.ATUALIZAR_COORDENADAS_DO_PONTO);
                      }
                  };

                  adicionaPontoFeatures = function(ponto) {
                      angular.forEach(vm.map.getLayers(), function(layer) {
                          if (layer.getSource() instanceof ol.source.Cluster) {
                              layer.getSource().getSource().addFeature(ponto);
                          }
                      });
                  };

                  removeFeatures = function() {
                      angular.forEach(vm.map.getLayers(), function(layer) {
                          if (layer instanceof ol.layer.Vector) {
                              layer.getSource().clear();
                              if (layer.getSource() instanceof ol.source.Cluster) {
                                  layer.getSource().getSource().clear();
                              }

                          }
                      });
                  };

                  removerFeaturesUnidadeAutonoma = function(uuidPonto, uuidTerreno, unidadesAutonomas) {

                      angular.forEach(unidadesAutonomas, function(unidadeAutonoma) {
                          angular.forEach(vm.map.getLayers().getArray(), function(layer) {
                              /* TODO: alterar nome da layer unidade autônoma que está sendo passada como parâmetro
                               após o exército criar um serviço para unidade autonôma. Hoje está benfeitoria.
                               *  */
                              removeFeatureUnidadeAutonomaByNameLayer(layer, "benfeitoria", unidadeAutonoma.edificacao.uuid);
                          });
                      });

                      angular.forEach(vm.map.getLayers().getArray(), function(layer) {
                          removeFeatureUnidadeAutonomaByNameLayer(layer, featureIds.TERRENO_CARTORIAL, uuidTerreno);
                          removeFeatureUnidadeAutonomaByNameLayer(layer, featureIds.TERRENO_CADASTRAL, uuidTerreno);
                          removeFeatureUnidadeAutonomaByNameLayer(layer, camadas.PONTO_UNIDADE_AUTONOMA, uuidPonto);
                      });
                  };

                  removeFeatureUnidadeAutonomaByNameLayer = function(layer, name, uuid) {
                      var feature;

                      if (layer.get('name') === camadas.PONTO_UNIDADE_AUTONOMA) {
                          feature = getFeatureByUuid(layer.getSource().getSource(), uuid);
                          if (feature) {
                              if (!featuresRemovidasUnidadeAutonomas.includes(feature)) {
                                  featuresRemovidasUnidadeAutonomas.push(feature);
                                  layer.getSource().getSource().removeFeature(feature);
                              }

                          }
                      }
                      if (layer.get('name') === name) {
                          feature = getFeatureByUuid(layer.getSource(), uuid);
                          if (feature) {
                              if (!featuresRemovidasUnidadeAutonomas.includes(feature)) {
                                  featuresRemovidasUnidadeAutonomas.push(feature);
                                  layer.getSource().removeFeature(feature);
                              }

                          }
                      }
                  };

                  getFeatureByUuid = function(source, uuid) {
                      var features = source.getFeatures();
                      for (var i = 0; i < features.length; i++) {
                          if (features[i].get('co_uuid') === uuid) {
                              return features[i];
                          }
                      }
                  };

                  removeAndSetPonto = function(feature) {
                      var featurePonto = imovelSource.getFeatureById(featureIds.PONTO_IMOVEL);
                      if (featurePonto) {
                          imovelSource.removeFeature(featurePonto);
                      }
                      if (!feature.getGeometry()) {
                          if (geometriaImovelPonto) {
                              feature.setGeometry(geometriaImovelPonto);
                          }
                      }
                      feature.setId(featureIds.PONTO_IMOVEL);
                      feature.setStyle(pontoImovelValidoStyle);
                      imovelSource.addFeature(feature);
                      return feature;
                  };

                  removeAndSetPontoTestada = function(feature) {
                      var featurePonto = imovelSource.getFeatureById(featureIds.PONTO_IMOVEL_TESTADA);
                      if (featurePonto) {
                          imovelSource.removeFeature(featurePonto);
                      }
                      if (!feature.getGeometry()) {
                          if (geometriaImovelPontoTestada) {
                              feature.setGeometry(geometriaImovelPontoTestada);
                          }
                      }
                      feature.setId(featureIds.PONTO_IMOVEL_TESTADA);
                      feature.setStyle(pontoImovelValidoTestadaStyle);
                      imovelSource.addFeature(feature);
                      return feature;
                  };

                  function validarPonto(feature) {
                      var featureJson;
                      var uuidTerreno = imovel.terreno.uuid;

                      featureJson = geoJson.writeFeatureObject(feature).geometry;
                      vmr.geomPonto = feature;

                      PontoImovel.save({ 'featureJson': featureJson, 'uuidTerreno': uuidTerreno }).then(function(result) {
                          var confirm,
                              pontoValido = true;

                          // FIXME impedindo validação de sobreposição de ponto do imóvel devido a migração de imóveis do sistema SPIUnet
                          // remova essa linha depois que todos os imóveis migrados forem corrigidos.
                          //result.sobrepostoTerreno = false;

                          if ($rootScope.usuarioLogado.jurisdicoes.includes(result.sgUf)) {
                              if (result.sobrepostoTerreno) {
                                  fecharFullScreen();
                                  MensagensService.exibirMensagemErro("Não é possível criar outro ponto sobre este imóvel.");
                                  pontoValido = false;
                              } else {
                                  if (result.noMunicipio !== scope.imovel.enderecamento.municipio && scope.imovel.enderecamento.municipio) {
                                      confirm = $mdDialog.confirm({
                                          focusOnOpen: false,
                                          textContent: "O ponto do imóvel deve estar no mesmo município informado nos dados de localização. Deseja continuar?",
                                          ok: "Sim",
                                          cancel: "Não"
                                      });

                                      $mdDialog.show(confirm).then(function() {

                                      }, function() {
                                          pontoValido = false;
                                          scope.imovel.pontoImovel = undefined;
                                          removerFeatureById(featureIds.PONTO_IMOVEL);
                                          feature.setId(featureIds.PONTO_IMOVEL);
                                          feature.setStyle(pontoImovelInvalidoStyle);
                                          vm.removerFeaturesCadastradas();
                                      });
                                  }
                              }
                          } else {
                            if (result.sgUf==null) {
                              //Ajuste para aceitar CEP genérico Demanda 4100565
                              pontoValido = true;
                            } else if (!isEspelhoDAgua(scope.imovel.tipoImovel)) {
                              MensagensService.exibirMensagemErro("Você não possui permissão para inserir imóveis nesta UF.");
                              pontoValido = false;
                            }
                          }
                          if (pontoValido) {
                              scope.imovel.pontoImovel = featureJson;
                              if (!scope.imovel.naturezaImovel) {
                                  scope.imovel.naturezaImovel = {};
                              }
                              feature.setId(featureIds.PONTO_IMOVEL);
                              feature.setStyle(pontoImovelValidoStyle);


                             //validarNaturezaImovel(scope.imovel.naturezaImovel);
                             if (result.noNaturezaImovel == null || isEspelhoDAgua(scope.imovel.tipoImovel)) {
                              scope.imovel.naturezaImovel.id = ID_NATUREZA_IMOVEL_INDEFINIDO;
                              scope.imovel.naturezaImovel.descricao = "Não se Aplica";
                            } else {
                                scope.imovel.naturezaImovel.descricao = result.noNaturezaImovel;
                                validarNaturezaImovel(scope.imovel.naturezaImovel);
                            }

                          } else {
                              scope.imovel.pontoImovel = undefined;
                              removerFeatureById(featureIds.PONTO_IMOVEL);
                              feature.setId(featureIds.PONTO_IMOVEL);
                              feature.setStyle(pontoImovelInvalidoStyle);
                          }
                          NotificadorService.notify(scope, { feature: feature, isValido: pontoValido }, notifiers.ADICIONAR_FEATURE_PONTO);
                      }).catch(function() {
                          MensagensService.exibirMensagemErro("GeoServiço indisponível! Por favor, tente novamente mais tarde.");
                      });
                  }

                  /**
                   * @description Funcao para validar o tipo da Natureza, e retornar o ID referente
                   * @param {*} naturezaImovel
                   */
                  function validarNaturezaImovel(naturezaImovel) {
                      if (naturezaImovel.descricao === "Urbano") {
                          scope.imovel.naturezaImovel.id = ID_NATUREZA_IMOVEL_URBANO;
                      }

                      if (naturezaImovel.descricao === "Rural") {
                          scope.imovel.naturezaImovel.id = ID_NATUREZA_IMOVEL_RURAL;
                      }
                  }

                  function removerFeaturesByName(name) {
                      angular.forEach(imovelSource.getFeatures(), function(value) {
                          if (value.get('name') === name) {
                              imovelSource.removeFeature(value);
                          }
                      });
                  }

                  function adicionarCentroSegmentos() {
                      var featureCadastral = imovelSource.getFeatureById(featureIds.TERRENO_CADASTRAL),
                          featureCartorial = imovelSource.getFeatureById(featureIds.TERRENO_CARTORIAL),
                          polygon,
                          line,
                          ponto,
                          centroFeature,
                          i;

                      removerFeaturesByName(featureIds.CENTRO_SEGMENTO);

                      if (featureCartorial) {
                          polygon = featureCartorial.getGeometry();
                      } else if (featureCadastral) {
                          polygon = featureCadastral.getGeometry();
                      }

                      if (polygon) {
                          for (i = 1; i < polygon.getCoordinates()[0].length; i += 1) {
                              line = new ol.geom.LineString([polygon.getCoordinates()[0][i - 1], polygon.getCoordinates()[0][i]]);
                              ponto = new ol.geom.Point(ol.extent.getCenter(line.getExtent()));

                              centroFeature = new ol.Feature({
                                  geometry: ponto,
                                  name: featureIds.CENTRO_SEGMENTO
                              });
                              centroFeature.setStyle(centroSegmentoStyle);
                              imovelSource.addFeature(centroFeature);
                          }
                      }

                  }

                  function removerFeatureById(id) {
                      var feature = imovelSource.getFeatureById(id);

                      if (feature) {
                          imovelSource.removeFeature(feature);
                      }
                  }

                  function apagarFeature(id) {
                      removerFeatureById(id);
                      MapaService.removeFeature(scope, id);
                      NotificadorService.notify(scope, id, notifiers.FEATURE_EXCLUIDA);
                  }

                  function removerFeatureTerrenoSobreposto(tipoTerreno) {
                      if (tipoTerreno === featureIds.TERRENO_CADASTRAL) {
                          removerFeaturesByName(featureIds.TERRENO_CADASTRAL_SOBREPOSTO);
                      } else {
                          removerFeaturesByName(featureIds.TERRENO_CARTORIAL_SOBREPOSTO);
                      }
                  }

                  function adicionarFeatureTerrenoSobreposto(intersecoes, tipoTerreno) {
                      var i;
                      if (intersecoes.length > 0) {
                          var id = tipoTerreno === featureIds.TERRENO_CARTORIAL ? featureIds.TERRENO_CARTORIAL_SOBREPOSTO : featureIds.TERRENO_CADASTRAL_SOBREPOSTO;

                          for (i = 0; i < intersecoes.length; i += 1) {
                              setFeatureSobreposicao(geoJson.readGeometry(intersecoes[i]), id);
                          }
                      }
                  }

                  function adicionarFeatureBenfeitoriaSobreposta(intersecoes) {
                      var i;
                      if (intersecoes.length > 0) {
                          for (i = 0; i < intersecoes.length; i += 1) {
                              setFeatureSobreposicao(geoJson.readGeometry(intersecoes[i]), featureIds.TERRENO_BENFEITORIA_SOBREPOSTO);
                          }
                      }
                  }

                  function adicionarFeatureUnidadeAutonomaSobreposta(intersecoes) {
                      var i;
                      if (intersecoes.length > 0) {
                          for (i = 0; i < intersecoes.length; i += 1) {
                              setFeatureSobreposicao(geoJson.readGeometry(intersecoes[i]), featureIds.UNIDADE_AUTONOMA_SOBREPOSTA);
                          }
                      }
                  }

                  function validarGeometriaTerreno(featureTerreno, tipoTerreno, tipoDesenho, terreno) {
                      var featureJson = geoJson.writeFeatureObject(featureTerreno).geometry,
                          confirm;
                      var uuidTerreno;

                      if (scope.imovel.terreno.uuid == null) {
                          uuidTerreno = "";
                      } else {
                          uuidTerreno = scope.imovel.terreno.uuid
                      }

                      /**
                       * Removendo validação de terreno devido a migração de imóveis SPIUnet
                       * A validação deve ser reabilitada assim que os imóveis migrados forem corrigidos
                       * */
                      // ValidaPontoTerrenoService.save({
                      //     'geometriaTerreno': featureJson,
                      //     'uuidTerreno': uuidTerreno
                      // }).$promise.then(function(result) {
                      var naoValidaPontoTerrenoPromise = new Promise(function (resolve, reject) {
                          resolve({intersecoes: [], errados: []});
                      });
                      naoValidaPontoTerrenoPromise.then(function(result) {

                          var isValido = result.intersecoes.length === 0 && result.errados.length === 0,
                              terrenoSobreposto;

                          removerFeatureById(tipoTerreno);

                          removerFeatureTerrenoSobreposto(tipoTerreno);

                          if (result.intersecoes.length > 0) {
                              terrenoSobreposto = result.intersecoes;
                          } else if (result.errados.length > 0) {
                              terrenoSobreposto = result.errados;
                          }

                          if (terrenoSobreposto) {
                              adicionarFeatureTerrenoSobreposto(terrenoSobreposto, tipoTerreno);
                          }

                          if (isValido) {
                              setStyleTerrenoByFeature(featureTerreno, tipoTerreno);
                          } else {
                              featureTerreno.setStyle(terrenoInvalidoStyle);
                          }

                          featureTerreno.setId(tipoTerreno);
                          featureTerreno.set('tipoDesenho', tipoDesenho);
                          imovelSource.addFeature(featureTerreno);

                          if (tipoTerreno === featureIds.TERRENO_CARTORIAL) {

                              scope.imovel.terreno.geometriaTerrenoCartorial = featureJson;
                              if (!imovelSource.getFeatureById(featureIds.TERRENO_CADASTRAL)) {
                                  adicionarCentroSegmentos();
                              } else {

                                  var featureCadastral = imovelSource.getFeatureById(featureIds.TERRENO_CADASTRAL);

                                  if (featureCadastral.get('tipoDesenho') === 'vetorizacao') {
                                      confirm = $mdDialog.confirm({
                                          focusOnOpen: false,
                                          textContent: "Não será permitido um polígono sem precisão da geometria cadastral. Os dados informados da vetorização em tela serão perdidos. Deseja confirmar?",
                                          ok: "Sim",
                                          cancel: "Não"
                                      });

                                      $mdDialog.show(confirm).then(function() {
                                          removerFeaturesByName(featureIds.TERRENO_CADASTRAL_SOBREPOSTO);
                                          removerFeatureById(featureIds.TERRENO_CADASTRAL);
                                          scope.imovel.terreno.tipoDesenho = undefined;
                                          scope.imovel.terreno.geometriaTerreno = undefined;
                                      }, function() {
                                          removerFeaturesByName(featureIds.TERRENO_CARTORIAL_SOBREPOSTO);
                                          removerFeatureById(featureIds.TERRENO_CARTORIAL);
                                          NotificadorService.notify(scope, null, notifiers.DELETESHAPEFILECARTORIAL);
                                      }).finally(function() {
                                          adicionarCentroSegmentos();
                                      });
                                  }

                              }
                          }
                          if (tipoTerreno !== featureIds.TERRENO_CARTORIAL) {
                              adicionarCentroSegmentos();
                              NotificadorService.notify(scope, { geometria: featureJson, isValid: isValido }, notifiers.GEOMETRIA_TERRENO_CADASTRAL_ALTERADA);
                          }
                          setClickEvent();
                          MapaService.addTerreno(scope, { feature: featureTerreno, isValid: isValido, intersecoes: terrenoSobreposto });
                      }, function(error) {
                        console.log(error);
                      });
                  }

                  function inserirPonto(feature) {
                      feature.setId(featureIds.PONTO_IMOVEL);
                      var feature_, layer_, uuidTerreno, confirm, pixel, coordenadas;
                      coordenadas = feature.getGeometry().getCoordinates();
                      pixel = vm.map.getPixelFromCoordinate(coordenadas);

                      // feature_ = vm.map.forEachFeatureAtPixel(pixel, function(feature) {
                      //   return feature;
                      // });

                      angular.forEach(vm.layers, function(layerterreno) {
                        if (layerterreno.get('name') === featureIds.TERRENO_CADASTRAL ) {
                          layer_ =  layerterreno;
                        }
                       });

                        feature_ = vm.map.forEachFeatureAtPixel(pixel, function(feature, layer_) {
                          return feature;
                        });
                      // feature_ = vm.map.forEachFeatureAtPixel(pixel, function(feature, layer) {
                      //      return feature;
                      //  }, null, function(layer) {
                      //      return layer;
                      //          //layer.get('name') === featureIds.TERRENO_CADASTRAL ||
                      //          //layer.get('name') === featureIds.TERRENO_CARTORIAL ||
                      //          //layer.get('name') === "imovel";
                      //  });

                      if (feature_) {
                          //uuidTerreno = feature_.get('co_uuid');

                          if (coordenadas) {

                            ImovelPontoService.getImoveisByPontoService({ ponto: coordenadas  }).$promise.then(function(imovel) {

                                  if (imovel.tipoImovel && imovel.tipoImovel.tipoPai === "UNIDADE AUTÔNOMA") {
                                      confirm = $mdDialog.confirm({
                                          focusOnOpen: false,
                                          textContent: "Caso esteja tentando cadastrar uma unidade autônoma em terreno já cadastrado, o ponto será criado automaticamente e não será necessário cadastrar a geometria e os dados do terreno novamente. Deseja confirmar?",
                                          ok: "Sim",
                                          cancel: "Não"
                                      });


                                      fecharFullScreen();

                                      $mdDialog.show(confirm).then(function() {

                                          feature.setId(featureIds.PONTO_IMOVEL);
                                          scope.edificacoes = imovel.unidadesAutonomas;
                                          uuidTerreno = imovel.terreno.uuid;
                                          reaproveitarDadosImovelAndEdificacoes(imovel.toJSON(), uuidTerreno);
                                          NotificadorService.notify(scope, true, notifiers.PONTO_IN_UNIDADE_AUTONOMA);

                                          vm.showFormularioUnidadeAutonoma = function() {
                                              return true;
                                          }

                                          //criarFeatures(scope.edificacoes,vm.map);
                                          //criarFeatures(null, vm.map);

                                          vm.showMapaEdificacaoCadastrada = true;
                                          $rootScope.$broadcast('setaTrueGeometriaValida');
                                          NotificadorService.notify(scope, true, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                                      }, function() {
                                          removerFeatureById(featureIds.PONTO_IMOVEL);
                                      });

                                  } else {
                                      feature_ = imovelSource.getClosestFeatureToCoordinate([feature.getGeometry().getExtent()[0], feature.getGeometry().getExtent()[1]], function(feature) {
                                          return feature.get('name') === featureIds.CENTRO_SEGMENTO;
                                      });

                                      if (feature_) {
                                          feature.setGeometry(feature_.getGeometry());
                                      }

                                      feature.setStyle(pontoImovelValidoStyle);
                                      validarPonto(feature);
                                  }
                              });
                          } else {
                              //console.log('passei');
                          }
                      } else {
                          validarPonto(feature);
                          NotificadorService.notify(scope, false, notifiers.PONTO_IN_UNIDADE_AUTONOMA);
                      }
                  }

                  var edificacaoSource = new ol.source.Vector();

                  vm.$on('carregaterreno', function(event, args) {
                      criarFeatures(scope.edificacoes, args.any);
                  });

                  vm.$on('deselecionarEdificacoes', function() {
                    angular.forEach(edificacaoSource.getFeatures(), function(feature){
                      feature.setStyle(ciMapaStyleFactory.terrenoVerde());
                    });
                  });

                  vm.$on('marcarEdificacaoSelecionada', function(event, args) {
                    angular.forEach(edificacaoSource.getFeatures(), function(feature){
                      if (args.uuid === feature.getProperties().dadosEdificacao.uuid){
                        feature.setStyle(null);
                        feature.setStyle(ciMapaStyleFactory.terrenoAmarelo());
                      }
                    });
                  });

                  function fecharFullScreen(){
                      if(document.exitFullscreen) {
                          document.exitFullscreen();
                      } else if(document.mozCancelFullScreen) {
                          document.mozCancelFullScreen();
                      } else if(document.webkitExitFullscreen ) {
                          document.webkitExitFullscreen();
                      }
                  }

                  function criarFeatures(edificacoes, map) {
                      /* var featureTerreno = new ol.Feature({
                           geometry: terreno
                       });
                       featureTerreno.setStyle(ciMapaStyleFactory.terrenoVerde());
                       terrenoSource.addFeature(featureTerreno);*/
                      if (edificacoes && edificacoes.length > 0) {
                          angular.forEach(edificacoes, function(dados) {
                              var featureEdificacao = new ol.Feature({
                                  geometry: geoJson.readGeometry(dados.edificacao.geometria)
                              });
                              featureEdificacao.setId(dados.edificacao.id);
                              featureEdificacao.set("dadosEdificacao", dados.edificacao);
                              featureEdificacao.set("condicoesAcessibilidade", dados.condicoesAcessibilidade);
                              featureEdificacao.setStyle(ciMapaStyleFactory.terrenoVerde());
                              edificacaoSource.addFeature(featureEdificacao);
                          });
                      }
                      var edificacaoLayer = new ol.layer.Vector({
                          source: edificacaoSource,
                          name: "edificacaoLayer"
                      });
                      map.addLayer(edificacaoLayer);
                  }

                  vm.desbloquearMapa = function() {
                      vm.bloquearMapa = false;
                      vm.marcarPonto();
                  };

                  vm.desbloquearTelaTerreno = function() {
                      vm.bloquearMapaTerreno = false;
                      vm.desenharTerreno();
                  };

                  vm.desbloquearTelaUnidadeAutonoma = function() {
                      vm.bloquearMapaUnidadeAutonoma = false;
                      vm.desenharUnidadeAutonoma();
                  };

                  vm.desbloquearTelaBenfeitoria = function() {
                      vm.bloquearMapaBenfeitoria = false;
                      vm.desenharBenfeitoria();
                  };

                  vm.marcarPonto = function() {
                      if (viewFac.getView().getZoom() >= 13) {

                          removerCustomInteractions();
                          setDrawPoint();

                          vm.map.addInteraction(drawPoint);

                          drawPoint.on('drawend', function(e) {
                              /*if (vmr.geometriaMun != undefined) {
                                  if (ciMapaValidacaoFactory.validaCepPontoMunicipio(vmr.geometriaMun, e.feature.getGeometry().getCoordinates())) {
                                      inserirPonto(e.feature);
                                      NotificadorService.notify(scope, e.feature.getGeometry().getCoordinates(), notifiers.ATUALIZAR_COORDENADAS_DO_PONTO);
                                  } else {
                                    if (!isEspelhoDAgua(scope.imovel.tipoImovel)) {
                                        MensagensService.exibirMensagemErro("O ponto do imóvel não se encontra sobre o município informado em relação ao CEP.");
                                     } else {
                                       inserirPonto(e.feature);
                                       NotificadorService.notify(scope, e.feature.getGeometry().getCoordinates(), notifiers.ATUALIZAR_COORDENADAS_DO_PONTO);
                                     }
                                  }
                              } else {*/
                                  inserirPonto(e.feature);
                                  NotificadorService.notify(scope, e.feature.getGeometry().getCoordinates(), notifiers.ATUALIZAR_COORDENADAS_DO_PONTO);
                              //}

                          });
                      } else {
                          MensagensService.exibirMensagemErro("Para incluir um ponto é necessário a aproximação igual ou inferior a 2000 metros.");
                      }
                  };

                  vm.marcarPontoTestada = function() {
                      if (viewFac.getView().getZoom() >= 13) {
                          removerCustomInteractions();

                          setDrawPoint();

                          vm.map.addInteraction(drawPoint);

                          drawPoint.on('drawstart', function() {
                              vm.map.unByKey(mapClickEvent);
                          });

                          drawPoint.on('drawend', function(e) {
                              /*if (vmr.geometriaMun != undefined) {
                                  if (ciMapaValidacaoFactory.validaCepPontoMunicipio(vmr.geometriaMun, e.feature.getGeometry().getCoordinates())) {
                                      adicionarMarcadorTestada(e.feature.getGeometry().getCoordinates());
                                      NotificadorService.notify(scope, e.feature.getGeometry().getCoordinates(), notifiers.ATUALIZAR_COORDENADAS_DO_PONTO_TESTADA);
                                  } else {
                                      MensagensService.exibirMensagemErro("O ponto do imóvel não se encontra sobre o município informado em relação ao CEP.");
                                  }
                              } else {*/
                                  adicionarMarcadorTestada(e.feature.getGeometry().getCoordinates());
                                  NotificadorService.notify(scope, e.feature.getGeometry().getCoordinates(), notifiers.ATUALIZAR_COORDENADAS_DO_PONTO_TESTADA);
                              //}

                              vm.map.removeInteraction(drawPoint);
                              imovelSource.once('addfeature', function(event) {
                                  var feature = event.feature;
                                  imovelSource.removeFeature(feature);
                              });

                          });

                      } else {
                          MensagensService.exibirMensagemErro("Para incluir um ponto é necessário a aproximação igual ou inferior a 2000 metros.");
                      }
                  };

                  function adicionarMarcadorTestada(coordinate) {
                      var feature = imovelSource.getClosestFeatureToCoordinate(coordinate, function(feature) {
                              return feature.get('name') === featureIds.CENTRO_SEGMENTO;
                          }),
                          pontoFeature;

                      if (feature) {
                          pontoFeature = new ol.Feature({
                              geometry: feature.getGeometry()
                          });
                          validarPontoTestada(pontoFeature);
                          NotificadorService.notify(scope, feature.getGeometry().getCoordinates(), notifiers.ATUALIZAR_COORDENADAS_DO_PONTO_TESTADA);
                      }
                  }

                  /*
                  O método está replicado como o do outro ponto, scope.imovel.pontoImovel possivelmente será trocado
                  */
                  function validarPontoTestada(feature) {
                      var featureJson;
                      var uuidTerreno = imovel.terreno.uuid;

                      featureJson = geoJson.writeFeatureObject(feature).geometry;
                      vmr.geomPonto = feature;

                      PontoImovel.save({ 'featureJson': featureJson, 'uuidTerreno': uuidTerreno }).then(function(result) {
                          var confirm,
                              pontoValido = true;

                          // FIXME impedindo validação de sobreposição de ponto do imóvel devido a migração de imóveis do sistema SPIUnet
                          // remova essa linha depois que todos os imóveis migrados forem corrigidos.
                          //result.sobrepostoTerreno = false;

                          if ($rootScope.usuarioLogado.jurisdicoes.includes(result.sgUf)) {
                              if (result.sobrepostoTerreno) {
                                  fecharFullScreen();
                                  MensagensService.exibirMensagemErro("Não é possível criar outro ponto sobre este imóvel.");
                                  pontoValido = false;
                              } else {
                                  if (result.noMunicipio !== scope.imovel.enderecamento.municipio && scope.imovel.enderecamento.municipio) {
                                      confirm = $mdDialog.confirm({
                                          focusOnOpen: false,
                                          textContent: "O ponto do imóvel deve estar no mesmo município informado nos dados de localização. Deseja continuar?",
                                          ok: "Sim",
                                          cancel: "Não"
                                      });

                                      $mdDialog.show(confirm).then(function() {

                                      }, function() {
                                          pontoValido = false;
                                          scope.imovel.pontoImovelTestada = undefined;
                                          removerFeatureById(featureIds.PONTO_IMOVEL_TESTADA);
                                          feature.setId(featureIds.PONTO_IMOVEL_TESTADA);
                                          feature.setStyle(pontoImovelInvalidoStyle);
                                      });
                                  }
                              }
                          } else {
                            if (!isEspelhoDAgua(scope.imovel.tipoImovel)) {
                              MensagensService.exibirMensagemErro("Você não possui permissão para inserir imóveis nesta UF.");
                              pontoValido = false;
                            }
                          }
                          if (pontoValido) {
                              scope.imovel.pontoImovelTestada = featureJson;
                              if (!scope.imovel.naturezaImovel) {
                                  scope.imovel.naturezaImovel = {};
                              }
                              feature.setId(featureIds.PONTO_IMOVEL_TESTADA);
                              feature.setStyle(pontoImovelValidoStyle);
                              if (result.noNaturezaImovel == null || isEspelhoDAgua(scope.imovel.tipoImovel)) {
                                scope.imovel.naturezaImovel.id = ID_NATUREZA_IMOVEL_INDEFINIDO;
                                scope.imovel.naturezaImovel.descricao = "Não se Aplica";
                              } else {
                                  scope.imovel.naturezaImovel.descricao = result.noNaturezaImovel;
                                  validarNaturezaImovel(scope.imovel.naturezaImovel);
                              }

                          } else {
                              scope.imovel.pontoImovel = undefined;
                              removerFeatureById(featureIds.PONTO_IMOVEL_TESTADA);
                              feature.setId(featureIds.PONTO_IMOVEL_TESTADA);
                              feature.setStyle(pontoImovelInvalidoStyle);
                          }
                          NotificadorService.notify(scope, { feature: feature, isValido: pontoValido }, notifiers.ADICIONAR_FEATURE_PONTO_TESTADA);
                      }).catch(function() {
                          MensagensService.exibirMensagemErro("GeoServiço indisponível! Por favor, tente novamente mais tarde.");
                      });
                  }


                  vm.voltar = function() {
                      var geometria;
                      if (vmr.geometriaCep) {
                          geometria = geoJson.readGeometry(vmr.geometriaCep);
                          viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());
                      }
                  };

                  vm.desenharTerreno = function() {
                      if (viewFac.getView().getZoom() >= 13) {
                          removerCustomInteractions();
                          setDrawPolygon();
                          vm.map.addInteraction(drawPolygon);

                          drawPolygon.on('drawstart', function() {
                              vm.map.unByKey(mapClickEvent);
                          });

                          drawPolygon.on('drawend', function(e) {
                              validarGeometriaTerreno(e.feature, featureIds.TERRENO_CADASTRAL, "vetorizacao");
                              NotificadorService.notify(scope, false, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                              vm.map.removeInteraction(drawPolygon);
                          });
                      } else {
                          MensagensService.exibirMensagemErro("Para vetorizar a área do terreno é necessário a aproximação igual ou inferior a 2000 metros.");
                      }
                  };

                  function toBenfeitoriaDTO(geometriaJson) {
                      return {
                          tipo: 1,
                          geom: {
                              geometry: geometriaJson,
                              type: geometriaJson.type
                          }
                      };
                  }

                  function getFeatureTerreno() {
                      var terreno;

                      if (imovelSource.getFeatureById(featureIds.TERRENO_CARTORIAL)) {
                          terreno = imovelSource.getFeatureById(featureIds.TERRENO_CARTORIAL);
                      } else {
                          terreno = imovelSource.getFeatureById(featureIds.TERRENO_CADASTRAL);
                      }
                      var geome = geoJson.writeFeatureObject(terreno).geometry;
                      return geome;
                  }

                  function getBenfeitorias(codigo) {

                      var benfeitorias = [],
                          benfeitoriasImovel = scope.imovel.benfeitorias || [];

                      for (var i = 0; i < benfeitoriasImovel.length; i++) {
                          if (benfeitoriasImovel[i].geometriaBenfeitoria && benfeitoriasImovel[i].tipoBenfeitoria.id === 1
                                && benfeitoriasImovel[i].ativa == true) {
                                    var benfeitoria = toBenfeitoriaDTO(benfeitoriasImovel[i].geometriaBenfeitoria);

                                    if (benfeitoriasImovel[i].codigo !== codigo) {
                                        benfeitorias.push(benfeitoria);
                                    }
                          }
                      }

                      return benfeitorias;
                  }

                  function validarGeometriaBenfeitoria(featureBenfeitoria, codigo, tipoDesenho) {

                      var benfeitoria,
                          geometriaBenfeitoria = geoJson.writeFeatureObject(featureBenfeitoria).geometry,
                          featureTerreno,
                          benfeitorias;

                      benfeitoria = toBenfeitoriaDTO(geometriaBenfeitoria);
                      featureTerreno = getFeatureTerreno();
                      benfeitorias = getBenfeitorias(codigo);

                      if (vm.imovel.enderecamento.ufImovel) {
                        var ufTemp = vm.imovel.enderecamento.ufImovel;
                        var munTemp = vm.imovel.enderecamento.municipioImovel;
                      }

                      angular.forEach(benfeitorias, function(item) {
                        if(item.ativa) {
                          benfeitorias.push(item);
                        }
                      });

                      ValidaTerrenoBenfeitoriaService.validar({
                          geometriaTerreno: featureTerreno,
                          benfeitorias: benfeitorias
                      }).$promise.then(function(result) {

                          var isValido = result.intersecoes.length === 0 && result.errados.length === 0;

                          removerFeatureById(codigo);
                          removerFeaturesByName(featureIds.TERRENO_BENFEITORIA_SOBREPOSTO);
                          adicionarFeatureBenfeitoriaSobreposta(result.intersecoes);

                          if (isValido) {
                              featureBenfeitoria.setStyle(benfeitoriaStyle);
                          } else {
                              featureBenfeitoria.setStyle(terrenoInvalidoStyle);
                          }

                          featureBenfeitoria.setId(codigo);
                          featureBenfeitoria.set('tipoDesenho', tipoDesenho);
                          featureBenfeitoria.set('name', 'benfeitoria');
                          imovelSource.addFeature(featureBenfeitoria);
                          NotificadorService.notify(scope, { geoBenfeitoria: geometriaBenfeitoria, isValid: isValido }, notifiers.GEOMETRIA_BENFEITORIA_ALTERADA);

                          if (vm.imovel.terreno.geometriaTerreno == undefined) {
                              vm.imovel.terreno.geometriaTerreno = getFeatureTerreno();
                          }

                          if (!vm.imovel.enderecamento.uf) {
                            vm.imovel.enderecamento.uf = ufTemp;
                            vm.imovel.enderecamento.municipio = munTemp;
                          }

                          NotificadorService.notify(scope, featureBenfeitoria, notifiers.ADICIONAR_FEATURE_BENFEITORIA);
                      });
                  }

                  vm.desenharBenfeitoria = function() {
                      if (viewFac.getView().getZoom() >= 13) {
                          removerCustomInteractions();
                          setDrawPolygonBenfeitoria();
                          vm.map.addInteraction(drawPolygonBenfeitoria);

                          drawPolygonBenfeitoria.on('drawend', function(e) {
                              var id = scope.benfeitoria.codigo || featureIds.ACESSAO_BENFEITORIA;

                              validarGeometriaBenfeitoria(e.feature, id, "vetorizacao");

                              vm.map.removeInteraction(drawPolygonBenfeitoria);
                          });
                      } else {
                          MensagensService.exibirMensagemErro("Para vetorizar a área da acessão/benfeitoria é necessário a aproximação igual ou inferior a 2000 metros.");
                      }
                  };

                  function getEdificacoes(id) {
                      var retorno = [],
                          edificacoes = vm.edificacoes || [];

                      for (var i = 0; i < edificacoes.length; i++) {
                          if (edificacoes[i].uuid !== id) {
                              var edificacao = toBenfeitoriaDTO(edificacoes[i].geometria);
                              retorno.push(edificacao);
                          }
                      }

                      return retorno;
                  }

                  scope.$on('atualizaEdificacaoUnidadeAutonoma', function(event, args) {
                      var uudiEdificacaoSelecionada = args.uudiEdificacaoSelecionada;
                      var featureParaAmarelo;
                      var layerUt;
                      var mapa = vm.map;
                      mapa.getLayers().forEach(function(layer) {
                          if (layer.get('name') != undefined && (layer.get('name') === 'benfeitoria' || layer.get('name') === 'pontoUnidadeAutonoma' )) {
                              layerUt = layer;
                              //Não está encontrando
                              featureParaAmarelo = getFeatureByUuid(layerUt.getSource(), uudiEdificacaoSelecionada);

                              //  angular.forEach(layerUt.getSource().getFeatures(), function(feature){
                              //     feature.setStyle(ciMapaStyleFactory.terrenoVerde());
                              //  });
                          }
                      });
                      //featureParaAmarelo.setStyle(ciMapaStyleFactory.terrenoAmarelo());
                      if (featureParaAmarelo != undefined) {
                          var amarelo = new ol.style.Style({
                              fill: new ol.style.Fill({
                                  color: 'rgba(255, 222, 0, 0.3)'
                              }),
                              stroke: new ol.style.Stroke({
                                      color: '#ffde00',
                                      width: 3
                                  })
                                  // zIndex: 6
                          });

                          featureParaAmarelo.setStyle(amarelo);
                          // featureAntigaParaAmarelo.setStyle(cinza);
                      }
                  });

                  function validarGeometriaUnidadeAutonoma(featureUnidadeAutonoma, id) {

                      var featureTerreno, tipoDesenho,
                          geometriaUnidadeAutonoma = geoJson.writeFeatureObject(featureUnidadeAutonoma).geometry,
                          edificacoes,
                          unidadeAutonoma;

                      unidadeAutonoma = toBenfeitoriaDTO(geometriaUnidadeAutonoma);
                      featureTerreno = getFeatureTerreno();
                      edificacoes = getEdificacoes(id);
                      tipoDesenho = 'VETORIZACAO';

                      edificacoes.push(unidadeAutonoma);

                      ValidaTerrenoBenfeitoriaService.validar({
                          geometriaTerreno: featureTerreno,
                          benfeitorias: edificacoes
                      }).$promise.then(function(result) {

                          var isValido = result.intersecoes.length === 0 && result.errados.length === 0;

                          var codigo = id || featureIds.UNIDADE_AUTONOMA;
                          removerFeatureById(codigo);
                          //removerFeaturesByName(featureIds.UNIDADE_AUTONOMA_SOBREPOSTA);
                          adicionarFeatureUnidadeAutonomaSobreposta(result.intersecoes);

                          if (isValido) {
                              featureUnidadeAutonoma.setStyle(unidadeAutonomaStyle);
                          } else {
                              featureUnidadeAutonoma.setStyle(terrenoInvalidoStyle);
                          }

                          NotificadorService.notify(scope, { geometria: geometriaUnidadeAutonoma, isValid: isValido }, notifiers.GEOMETRIA_UNIDADE_AUTONOMA_ALTERADA);


                          featureUnidadeAutonoma.setId(id);
                          featureUnidadeAutonoma.set('tipoDesenho', tipoDesenho);
                          featureUnidadeAutonoma.set('co_uuid', id);
                          imovelSource.addFeature(featureUnidadeAutonoma);

                          NotificadorService.notify(scope, featureUnidadeAutonoma, notifiers.ADICIONAR_FEATURE_UNIDADE_AUTONOMA);
                          NotificadorService.notify(scope, true, notifiers.VERIFICAR_PONTO_NA_ARESTA);

                          //if (vm.imovel.terreno.geometriaTerreno == undefined) {
                            vm.imovel.terreno.geometriaTerreno = getFeatureTerreno();
                          //}

                      });

                  }

                  vm.desenharUnidadeAutonoma = function() {
                      if (viewFac.getView().getZoom() >= 13) {
                          removerCustomInteractions();
                          setDrawPolygonUnidadeAutonoma();
                          vm.map.addInteraction(drawPolygonUnidadeAutonoma);

                          drawPolygonUnidadeAutonoma.on('drawend', function(e) {
                              var id = vm.imovel.unidadeAutonoma.edificacao.uuid || featureIds.UNIDADE_AUTONOMA;
                              validarGeometriaUnidadeAutonoma(e.feature, id);

                              vm.map.removeInteraction(drawPolygonUnidadeAutonoma);
                          });

                      } else {
                          MensagensService.exibirMensagemErro("Para vetorizar a área da unidade autônoma é necessário a aproximação igual ou inferior a 2000 metros.");
                      }
                  };

                  vm.renderBotaoApagar = function() {
                      var abas = [];

                      if (action === "add") {
                          abas = [ABA_LOCALIZACAO, ABA_TERRENO, ABA_UNIDADE_AUTONOMA, ABA_BENFEITORIA];
                      } else if (action === 'edit') {
                          abas = [ABA_TERRENO, ABA_UNIDADE_AUTONOMA, ABA_BENFEITORIA];
                      }

                      return abas.includes(vm.tabIndex);
                  };

                  vm.showBotaoApagar = function() {
                      var id = null;

                      if (vm.tabIndex === ABA_LOCALIZACAO) {
                          id = featureIds.PONTO_IMOVEL;
                      } else if (vm.tabIndex === ABA_TERRENO) {
                          id = featureIds.TERRENO_CADASTRAL;
                      } else if (vm.tabIndex === ABA_UNIDADE_AUTONOMA) {
                          id = featureIds.UNIDADE_AUTONOMA;
                      } else if (vm.tabIndex === ABA_BENFEITORIA) {
                          id = vm.benfeitoria.codigo || featureIds.ACESSAO_BENFEITORIA;
                      }

                      return !!imovelSource.getFeatureById(id);
                  };

                  vm.apagar = function() {
                      removerCustomInteractions();

                      if (vm.tabIndex === ABA_LOCALIZACAO && action === "add") {
                          apagarFeature(featureIds.PONTO_IMOVEL);

                          //centraliza
                          vm.map.getView().setCenter(ol.proj.fromLonLat(MAPA_CONSTANTS_INCORPORACAO.GEOMETRY.GEOMETRIA_INICIAL));
                          vm.map.getView().setZoom(4);

                          if (isUnidadeAutonoma(vm.imovel) && (vm.imovel.terreno.id != null || vm.imovel.terreno.id != undefined)){
                            apagarFeature(featureIds.TERRENO_CADASTRAL);
                            adicionarCentroSegmentos();
                            removerFeaturesByName(featureIds.TERRENO_CADASTRAL_SOBREPOSTO);
                            NotificadorService.notify(vm, { geometria: undefined, isValid: false }, notifiers.GEOMETRIA_TERRENO_CADASTRAL_ALTERADA);
                          }

                      } else if (vm.tabIndex === ABA_TERRENO) {
                        if (vm.imovel.terreno.id == null || vm.imovel.terreno.id == undefined){
                          apagarFeature(featureIds.TERRENO_CADASTRAL);
                          adicionarCentroSegmentos();
                          removerFeaturesByName(featureIds.TERRENO_CADASTRAL_SOBREPOSTO);
                          NotificadorService.notify(vm, { geometria: undefined, isValid: false }, notifiers.GEOMETRIA_TERRENO_CADASTRAL_ALTERADA);
                        }

                      } else if (vm.tabIndex === ABA_UNIDADE_AUTONOMA && action === "add") {
                          apagarFeature(featureIds.UNIDADE_AUTONOMA);
                          removerFeaturesByName(featureIds.UNIDADE_AUTONOMA_SOBREPOSTA);
                          NotificadorService.notify(vm, { geometria: undefined, isValid: false }, notifiers.GEOMETRIA_UNIDADE_AUTONOMA_ALTERADA);

                      } else if (vm.tabIndex === ABA_BENFEITORIA) {
                          var id = vm.benfeitoria.codigo || featureIds.ACESSAO_BENFEITORIA;

                          apagarFeature(id);
                          removerFeaturesByName(featureIds.TERRENO_BENFEITORIA_SOBREPOSTO);
                          NotificadorService.notify(vm, { geoBenfeitoria: undefined, isValid: false }, notifiers.GEOMETRIA_BENFEITORIA_ALTERADA);
                      }

                      vmr.geomPonto = undefined;
                  };

                  vm.alterarBingVisible = function() {
                      angular.forEach(vm.map.getLayers(), function(value) {
                          if (value.get('name') === 'bingMapa') {
                              value.setVisible(!value.getVisible());
                              NotificadorService.notify(scope, value.getVisible(), notifiers.ALTERAR_VISIBILIDADE_BING);
                          }
                      });

                  };

                  vm.alterarLayerVisible = function(name) {
                      angular.forEach(vm.map.getLayers(), function(value) {
                          if (value.get('name') === name) {
                              value.setVisible(!value.getVisible());
                          }
                      });
                  };

                  vm.layerFilter = function(layer) {
                      return !layer.get('required');
                  };

                  vm.direcionarDetalharLocalizacao = function(idImovel) {
                      if (angular.isFunction(scope.$parent.fechar)) {
                          scope.$parent.fechar();
                      }

                      if (idImovel) {
                          if ($state.current.name === 'INCORPORACAO.homologarImovel') {
                              $state.go('INCORPORACAO.homologarImovelDetalhar', { id: idImovel });
                          } else {
                              $state.go('INCORPORACAO.detalharImovel', { id: idImovel });
                          }
                      }
                  };

                  vm.direcionarDetalharPesquisar = function(imovelSelecionado) {
                      if (action === 'pesquisarImovel') {
                          $state.go('INCORPORACAO.detalharImovel', { id: imovelSelecionado.id });
                      } else {
                          scope.$parent.fechar();
                          $state.go('INCORPORACAO.homologarImovelDetalhar', { id: imovelSelecionado.id });
                      }

                  };

                  vm.alterarVisibilidadeBalaoCamadas = function() {
                      vm.listaLayers = !vm.listaLayers;
                  };

                  setStyleTerrenoByFeature = function(feature, tipoTerreno) {
                      if (tipoTerreno === featureIds.TERRENO_CADASTRAL) {
                          var terrenoCartorial = imovelSource.getFeatureById(featureIds.TERRENO_CARTORIAL);
                          if (terrenoCartorial) {
                              feature.setStyle(terrenoValidoPontilhadoStyle);
                          } else {
                              feature.setStyle(terrenoValidoStyle);
                          }
                      } else {
                          feature.setStyle(terrenoValidoStyle);
                      }
                  };

                  desenhaPorGeometria = function(geometry, tipoTerreno, acao) {
                      var feature, geometria;

                      if (acao === 'vetorizacao') {
                          geometria = geometry;
                      } else {
                          geometria = geoJson.readGeometry(geometry);
                      }

                      removerFeatureById(tipoTerreno);
                      removerFeaturesByName(featureIds.CENTRO_SEGMENTO);

                      feature = new ol.Feature({
                          geometry: geometria
                      });

                      feature.setId(tipoTerreno);
                      feature.set('tipoDesenho', acao);
                      imovelSource.addFeature(feature);
                      return feature;
                  };

                  destacarBenfeitoria = function(id) {
                      angular.forEach(imovelSource.getFeatures(), function(feature) {
                          if (feature.get('name') === 'benfeitoria') {
                              if (feature.getId() === id) {
                                  feature.setStyle(benfeitoriaDestacadaStyle);
                              } else {
                                  feature.setStyle(benfeitoriaStyle);
                              }
                          }
                      });
                  };

                  getImovelOverlayHTML = function() {

                      return "<div layout='row' ng-repeat='imovel in imoveisOverlay' ng-click='direcionarDetalharLocalizacao(imovel.id)'>" +
                          "<img class='overlay-image' ng-if='imovel.imagens[0]' src='data:image/jpeg;base64,{{imovel.imagens[0].imagem.arquivo}}'/>" +
                          "<img class='overlay-image' ng-if='!imovel.imagens[0]' src='./assets/img/no-image.png'/>" +
                          "<md-content class='overlay-content' style='margin-left: 10px'>" +
                          "<p><b>" +
                          "RIP: {{imovel.rip}} " +
                          "</b></p>" +
                          "<p><b>" +
                          "Endereço: {{imovel.enderecamento.tipoLogradouro + ' ' + imovel.enderecamento.logradouro + ' - ' + imovel.numero + ' ' + imovel.enderecamento.municipio + ' ' + imovel.enderecamento.bairro}}  " +
                          "</b></p>" +
                          "<p><b>" +
                          "CEP: {{imovel.enderecamento.cep}}" +
                          "</b></p>" +
                          "</md-content>" +
                          "</div>";

                  };

                  getImovelById = function(id) {
                      var i;
                      for (i = 0; i < vm.resultadoPesquisarImovel.length; i += 1) {
                          if (vm.resultadoPesquisarImovel[i].id === id) {
                              return vm.resultadoPesquisarImovel[i];
                          }
                      }
                  };

                  desenharGeometriaEdificacao = function(edificacao) {
                      var geometria = edificacao.geometria,
                          geometriaUnidadeAutonoma, featureUnidadeAutonoma;

                      if (geometria) {
                          geometriaUnidadeAutonoma = geoJson.readGeometry(geometria);
                          removerFeatureById(featureIds.UNIDADE_AUTONOMA);
                          featureUnidadeAutonoma = new ol.Feature({});
                          featureUnidadeAutonoma.setId(edificacao.uuid);
                          featureUnidadeAutonoma.setStyle(unidadeAutonomaStyle);
                          featureUnidadeAutonoma.set('tipoDesenho', 'vetorizacao');
                          featureUnidadeAutonoma.setGeometry(geometriaUnidadeAutonoma);
                          featureUnidadeAutonoma.set('co_uuid', edificacao.uuid);
                          imovelSource.addFeature(featureUnidadeAutonoma);
                          if (scope.tabIndex !== 3) {
                              NotificadorService.notify(scope, featureUnidadeAutonoma, notifiers.ADICIONAR_FEATURE_UNIDADE_AUTONOMA);
                          }
                      }
                  };

                  function reaproveitarDadosImovelAndEdificacoes(imovel, uuidTerreno) {

                      var uuidPonto, featureCadastral, featureCartorial, geometriaPonto, geometriaPontoTestada;
                      if (scope.imovel.tipoImovel && scope.imovel.tipoImovel.tipoPai !== tiposImovel.UNIDADE_AUTONOMA) {
                          scope.imovel.tipoImovel = undefined;
                          NotificadorService.notify(scope, undefined, notifiers.LIMPAR_TIPO_IMOVEL);
                      } else {
                          scope.imovel.tipoImovel = imovel.tipoImovel;
                      }
                      scope.imovel.enderecamento = imovel.enderecamento;
                      scope.imovel.pontoImovel = imovel.pontoImovel;
                      scope.imovel.pontoImovelTestada = imovel.pontoImovelTestada;
                      scope.imovel.terreno = Object.assign(scope.imovel.terreno, imovel.terreno);
                      scope.imovel.terreno.unidadeMedidaCadastral = 'm2';
                      scope.imovel.terreno.unidadeMedidaCartorial = 'm2';
                      scope.imovel.naturezaImovel = imovel.naturezaImovel;
                      scope.imovel.sistemaReferencia = imovel.sistemaReferencia;
                      scope.imovel.formatoCoordenadas = imovel.formatoCoordenadas;
                      uuidPonto = scope.imovel.uuid;

                      removerFeaturesUnidadeAutonoma(uuidPonto, uuidTerreno, imovel.unidadesAutonomas);

                      if (scope.imovel.terreno.geometriaTerrenoCartorial) {
                          featureCartorial = desenhaPorGeometria(scope.imovel.terreno.geometriaTerrenoCartorial, featureIds.TERRENO_CARTORIAL, null);
                          featureCartorial.set('co_uuid', uuidTerreno);
                          setStyleTerrenoByFeature(featureCartorial, featureIds.TERRENO_CARTORIAL);
                          MapaService.addTerreno(scope, { feature: featureCartorial, isValid: true, intersecoes: {} });
                      }

                      if (scope.imovel.terreno.geometriaTerreno) {
                          featureCadastral = desenhaPorGeometria(scope.imovel.terreno.geometriaTerreno, featureIds.TERRENO_CADASTRAL, null);
                          featureCadastral.set('co_uuid', uuidTerreno);
                          setStyleTerrenoByFeature(featureCadastral, featureIds.TERRENO_CADASTRAL);
                          MapaService.addTerreno(scope, { feature: featureCadastral, isValid: true, intersecoes: {} });
                          NotificadorService.notify(scope, { geometria: scope.imovel.terreno.geometriaTerreno, isValid: true }, notifiers.GEOMETRIA_TERRENO_CADASTRAL_ALTERADA);
                      }

                      angular.forEach(imovel.unidadesAutonomas, function(unidadeAutonoma) {
                          desenharGeometriaEdificacao(unidadeAutonoma.edificacao);
                      });

                      if (scope.imovel.pontoImovel) {
                          geometriaPonto = geoJson.readGeometry(scope.imovel.pontoImovel);
                          feature.setGeometry(geometriaPonto);
                          removeAndSetPonto(feature);
                          NotificadorService.notify(scope, { feature: feature, isValido: true }, notifiers.ADICIONAR_FEATURE_PONTO);
                      }

                      if (scope.imovel.pontoImovelTestada) {
                          geometriaPontoTestada = geoJson.readGeometry(scope.imovel.pontoImovelTestada);
                          feature.setGeometry(geometriaPontoTestada);
                          removeAndSetPontoTestada(feature);
                          NotificadorService.notify(scope, { feature: feature, isValido: true }, notifiers.ADICIONAR_FEATURE_PONTO_TESTADA);
                      }
                  }

                  redesenharFeaturesRemovidasUnidadeAutonoma = function() {
                      angular.forEach(featuresRemovidasUnidadeAutonomas, function(feature) {
                          if (feature.getGeometry() && feature.getGeometry().getType() != 'Point') {
                              feature.setStyle(styles.terrenoCinza());
                              if (feature.getId().includes('benfeitoria')) {
                                  adicionaFeatureByLayer('benfeitoria', feature);
                              }

                              if (feature.getId().includes('cadastral')) {
                                  adicionaFeatureByLayer(featureIds.TERRENO_CADASTRAL, feature);
                              }

                              if (feature.getId().includes('cartorial')) {
                                  adicionaFeatureByLayer(featureIds.TERRENO_CARTORIAL, feature);
                              }

                          } else if (feature.getGeometry() && feature.getGeometry().getType() == 'Point') {
                              adicionaFeatureByLayer(camadas.PONTO_UNIDADE_AUTONOMA, feature);
                          }
                      });
                  };

                  adicionaFeatureByLayer = function(layerName, feature) {

                      if (layerName == featureIds.PONTO_UNIDADE_AUTONOMA) {
                          angular.forEach(vm.map.getLayers().getArray(), function(layer) {
                              if (layer.get('name') === layerName) {
                                  layer.getSource().getSource().addFeature(feature);
                              }
                          });
                      } else {
                          angular.forEach(vm.map.getLayers().getArray(), function(layer) {
                              if (layer.get('name') === layerName) {
                                  layer.getSource().addFeature(feature);
                              }
                          });
                      }

                  };

                  vm.getImoveisRaio2000Km = function(extent) {
                      vm.removerFeaturesCadastradas();
                      if (extent && angular.isArray(extent)) {
                          var poly = turf.bboxPolygon(extent);

                          var idTerreno = scope.imovel && scope.imovel.terreno ? scope.imovel.terreno.id : undefined;

                          obterTerrenosIntersedidos.listarTerrenosIntersedidos({
                              'buffered': poly,
                              'idTerreno': idTerreno
                          }).then(function(res) {
                              var format = new ol.format.WKT();

                              angular.forEach(res.data, function(tipoTerrenoGeometrias) {

                                if (tipoTerrenoGeometrias.unidadeAutonoma){
                                  angular.forEach(tipoTerrenoGeometrias.geometriaTerrenos, function(geom) {
                                      var feature = new ol.Feature({
                                          geometry: format.readFeature(geom).getGeometry()
                                      });
                                      if (scope.tabIndex === -1 && scope.action == 'add') {
                                        vm.adicionarAreaCadastrada(feature, styles.terrenoAzul());
                                      } else{
                                        vm.adicionarAreaCadastrada(feature, styles.terrenoCinza());
                                      }

                                  });
                                } else {
                                  angular.forEach(tipoTerrenoGeometrias.geometriaTerrenos, function(geom) {
                                    var feature = new ol.Feature({
                                        geometry: format.readFeature(geom).getGeometry()
                                    });
                                    vm.adicionarAreaCadastrada(feature, styles.terrenoCinza());
                                });
                                }

                              });

                          });
                      }
                  }

                  vm.adicionarAreaCadastrada = function(feature, estilo) {
                      feature.setStyle(estilo);
                      vectorGeomCadastradosSource.addFeature(feature);
                  };

                  vm.removerFeaturesCadastradas = function() {
                      vectorGeomCadastradosSource.clear();
                  }

                  function onMoveEnd(evt) {
                      setTimeout(function() {
                          if (viewFac.getView().getZoom() >= 13) {
                              var map = evt.map;
                              var extent = map.getView().calculateExtent(map.getSize());
                              vm.getImoveisRaio2000Km(extent);
                          }
                      }, 2000);
                  }

                  setTimeout(init, 1000);
              });

          }
      };
  }]);
}());

(function(){
    "use strict";
    angular.module('su-cadastro-imovel')
        .service('camposAjudaService', ['$http', 'urlBase', service]);

    function service($http, urlBase){

        var buscarPorFuncionalidade = function(codigosSistemico){
            return $http.get(urlBase + 'ect/campos-ajuda/pesquisar-por-funcionalidade-codigo/' , {
                params: {
                    codigos: codigosSistemico
                }
            });
        };
        
        return {
            
            buscarPorFuncionalidade: buscarPorFuncionalidade
        };
    }
})();

(function () {
    "use strict";
    TipoImovelFactory.$inject = ["$http", "urlBase"];
    angular.module("su-cadastro-imovel").factory("TipoImovelFactory", TipoImovelFactory);

    function TipoImovelFactory($http, urlBase) {

        function buscarTodos() {
            return $http.get(urlBase + "dominio/tipo-imovel/all");
        }

        function isCavidadeNaturalSubterranea(tipoImovel) {
            return angular.isDefined(tipoImovel) && tipoImovel.id === 5;
        }

        function isUnidadeAutonoma(tipoImovel) {
            return angular.isDefined(tipoImovel) && tipoImovel.tipoPai === "UNIDADE AUTÔNOMA";
        }

        function isGlebaTerrenoLoteComEdificacao(tipoImovel) {
            return angular.isDefined(tipoImovel) && tipoImovel.id === 2;
        }

        function isEspelhoDagua(tipoImovel) {
            return angular.isDefined(tipoImovel) && tipoImovel.id === 3;
        }

        function isGaragem(tipoImovel) {
            return angular.isDefined(tipoImovel) && tipoImovel.id === 8;
        }

        return {
            buscarTodos: buscarTodos,
            isCavidadeNaturalSubterranea: isCavidadeNaturalSubterranea,
            isUnidadeAutonoma: isUnidadeAutonoma,
            isGlebaTerrenoLoteComEdificacao: isGlebaTerrenoLoteComEdificacao,
            isEspelhoDagua: isEspelhoDagua,
            isGaragem: isGaragem
        };
    }
})();

(function(){
  'use strict';
  angular.module('su-cadastro-imovel')
  .service('ProprietarioService', ["$http", "urlBase", function ($http, urlBase) {
    this.salvarUgSindica = function (proprietario) {
              return $http.post(urlBase + '/proprietario/ugsindica', proprietario);
            };
  }]);
})();

(function(){
    'use strict';
    angular.module("su-cadastro-imovel")
    .factory("PontoImovel", ["$resource", "urlBase", function($resource, urlBase){
      return $resource(urlBase + '/geometria/ponto/validar',
      {feature : '@feature'},
      {
        get : {
          method: 'GET',
          params : {
            feature : '@feature;'
          }
        }
      });
    }]);
})();

(function(){
	'use strict';
	angular.module('su-cadastro-imovel').factory('PesquisaRipsService', PesquisaRips);

	PesquisaRips.$inject = ['$resource', 'urlBase'];

	function PesquisaRips($resource, urlBase){
		return $resource(urlBase + 'imovel/pesquisarRIPs', {imovelPesquisa : '@imovelPesquisa',page: '@page', max: '@max', sort: '@sort'},
		{
			query: {
				method: 'GET',
				params: {
					imovelPesquisa: '@imovelPesquisa',
					page: '@page',
					max: '@max',
					sort: '@sort'
				}
			}
		});
	}

})();

(function(){
	'use strict';
	angular.module('su-cadastro-imovel').factory('PesquisaRIPsNaoPaginado', PesquisaRIPsNaoPaginado);

	PesquisaRIPsNaoPaginado.$inject = ['$resource', 'urlBase'];

	function PesquisaRIPsNaoPaginado($resource, urlBase){
		return $resource(urlBase + 'imovel/pesquisarRIPsNaoPaginado', {imovelPesquisa : '@imovelPesquisa'},
		{
			query: {
				method: 'GET',
				params: {
					imovelPesquisa: '@imovelPesquisa'
				},
				isArray: true
			}
		});
	}

})();

(function(){
	'use strict'
	angular.module('su-cadastro-imovel').service('modalService', service);

	service.$inject = ['$filter', '$mdDialog', '$state', 'Dominio', 'ectService', 'UfsService', 'MunicipioService',
	'PesquisaRipsService', 'PesquisaRIPsNaoPaginado', 'MensagensService'];

	function service($filter, $mdDialog, $state, Dominio, ectService, UfsService,PaisesService, MunicipioService, PesquisaRipsService, PesquisaRIPsNaoPaginado, MensagensService){
		var exibirSelecionarRIPs = function(constanteRetornoDados){
			return $mdDialog.show({
				controllerAs:'selecionarRIPsCtrl',
				controller: ["$scope", function($scope){
					var selecionarRIPsCtrl = this;
					selecionarRIPsCtrl.paises = PaisesService.query();
					selecionarRIPsCtrl.ufs = UfsService.UFS;
					selecionarRIPsCtrl.municipios = [];
					selecionarRIPsCtrl.isCEPPreenchido = false;
					selecionarRIPsCtrl.RIPSSelecionados = [];
					selecionarRIPsCtrl.query = {
						page: 1,
						limit: 5
					};

					selecionarRIPsCtrl.fechar = function(){
						$mdDialog.cancel();
					}

					selecionarRIPsCtrl.buscarMunicipiosPorUf = function(){
						if(selecionarRIPsCtrl.filtroRIPs.enderecamento.uf){
							selecionarRIPsCtrl.filtroRIPs.enderecamento.municipio = '';
							selecionarRIPsCtrl.municipios = MunicipioService.TodosMunicipios.query({uf: selecionarRIPsCtrl.filtroRIPs.enderecamento.uf});
						}
					};

					selecionarRIPsCtrl.pesquisar = function(){
						if(selecionarRIPsCtrl.filtroRIPs.enderecamento.municipio === ''){
							selecionarRIPsCtrl.filtroRIPs.enderecamento.municipio = null;
						}
						if(selecionarRIPsCtrl.filtroRIPs.enderecamento.geometria){
							selecionarRIPsCtrl.filtroRIPs.enderecamento.geometria = {};
						}
						var filtroSerializado = angular.toJson(selecionarRIPsCtrl.filtroRIPs);
						PesquisaRipsService.query({
							imovelPesquisa: filtroSerializado,
							page: selecionarRIPsCtrl.query.page,
							max: selecionarRIPsCtrl.query.limit
						}).$promise.then(function (data){
							if(data.content.length > 0){
								selecionarRIPsCtrl.resultadoPesquisa = data.content;
								selecionarRIPsCtrl.query.totalElements = data.totalElements;
								selecionarRIPsCtrl.query.totalPages = data.totalPages;
							}else{
								MensagensService.exibirMensagemErro("Nenhum RIP encontrado");
              					selecionarRIPsCtrl.resultadoPesquisa = [];
							}
						});

					};

					selecionarRIPsCtrl.isCepGenerico = false;
		     	 	selecionarRIPsCtrl.buscarEnderecoCep = function () {
				        var cep = selecionarRIPsCtrl.filtroRIPs.enderecamento.cep;
				        if (cep != undefined && cep.length == 8) {
				          ectService.get({cep: cep}).$promise.then(function (data) {
				            if (data != undefined) {

				              data.pais = Dominio.Constantes.Brasil;
				              if(selecionarRIPsCtrl.municipios.length > 0){
				              	selecionarRIPsCtrl.municipios = [];
				              }
				              selecionarRIPsCtrl.municipios.push(data.municipio);
				              selecionarRIPsCtrl.filtroRIPs.enderecamento = data;

				              if (selecionarRIPsCtrl.verificaCEPGenerico(cep)) {
				                selecionarRIPsCtrl.isCepGenerico = true;
				              } else {
				                selecionarRIPsCtrl.isCepGenerico = false;
				              }

				              	selecionarRIPsCtrl.isCEPPreenchido = true;
				            } else {
                                MensagensService.exibirMensagemErro("CEP não encontrado");
				            }
				          });

				        }
			      	};

			      	selecionarRIPsCtrl.limpar = function(){
			      		selecionarRIPsCtrl.filtroRIPs = {};
			      		selecionarRIPsCtrl.filtroRIPs.enderecamento = {};
			      		selecionarRIPsCtrl.filtroRIPs.enderecamento.pais = Dominio.Constantes.Brasil;
			      	};

			      	selecionarRIPsCtrl.page = function(){
			      		if(selecionarRIPsCtrl.filtroRIPs.enderecamento.municipio === ''){
							selecionarRIPsCtrl.filtroRIPs.enderecamento.municipio = null;
						}
						if(selecionarRIPsCtrl.filtroRIPs.enderecamento.geometria){
							selecionarRIPsCtrl.filtroRIPs.enderecamento.geometria = {};
						}
			      		var filtroSerializado = angular.toJson(selecionarRIPsCtrl.filtroRIPs);
						PesquisaRipsService.query({
							imovelPesquisa: filtroSerializado,
							page: selecionarRIPsCtrl.query.page,
							max: selecionarRIPsCtrl.query.limit
						}).$promise.then(function (data){

							if(selecionarRIPsCtrl.RIPSSelecionados.length > 0){
								angular.forEach(selecionarRIPsCtrl.RIPSSelecionados, function(value, key){
									angular.forEach(data.content, function(valueData, keyData){
										if(value === valueData.rip){
											valueData.isSelecionado = true;
										}
									});
								});
							}

							selecionarRIPsCtrl.resultadoPesquisa = data.content;
							if(selecionarRIPsCtrl.selecionarTodos){
								selecionarRIPsCtrl.marcarDesmarcarTodosCheckbox();
							}
						});
			      	};

			      	selecionarRIPsCtrl.atualizaMarcarTodosCheckbox = function(item){
			      		if(!item.isSelecionado){
			      			if(selecionarRIPsCtrl.selecionarTodos){
			      				selecionarRIPsCtrl.selecionarTodos = false;
			      			}
			      			angular.forEach(selecionarRIPsCtrl.RIPSSelecionados, function(value, key){
			      				if(value === item.rip){
			      					selecionarRIPsCtrl.RIPSSelecionados.splice(key, 1);
			      				}
			      			});
			      		}else{
			      			var ripNaoSelecionado = true;
			      			angular.forEach(selecionarRIPsCtrl.RIPSSelecionados, function(value, key){
			      				if(value === item.rip){
			      					ripNaoSelecionado = false;
			      				}
			      			});
			      			if(ripNaoSelecionado){
			      				selecionarRIPsCtrl.RIPSSelecionados.push(item.rip);
			      			}
			      		}
			      	};

			      	selecionarRIPsCtrl.confirmar = function(){
				      	if(!selecionarRIPsCtrl.selecionarTodos){
				      		if(selecionarRIPsCtrl.RIPSSelecionados.length === 0){
                                MensagensService.exibirMensagemErro("Necessário selecionar um imóvel.");
				      		}else{
				      			$mdDialog.hide(selecionarRIPsCtrl.RIPSSelecionados);
				      		}

				      	}else{
				      		var filtroSerializado = angular.toJson(selecionarRIPsCtrl.filtroRIPs);
				      		PesquisaRIPsNaoPaginado.query({
							imovelPesquisa: filtroSerializado
							}).$promise.then(function (data){
								angular.forEach(data, function(value,key){
									var isItemSelecionado = false;
									angular.forEach(selecionarRIPsCtrl.RIPSSelecionados, function(valueSelecionado, keySelecionado){
										if(valueSelecionado === value.rip){
											isItemSelecionado = true;
										}
									});
									if(!isItemSelecionado){
										selecionarRIPsCtrl.RIPSSelecionados.push(value.rip);
									}
								});
								$mdDialog.hide(selecionarRIPsCtrl.RIPSSelecionados);
							});
				      	}
			      	};

		      	 	selecionarRIPsCtrl.verificaCEPGenerico = function (cep) {
        				if (cep.endsWith("000")) {
          					return true;
        				} else {
          					return false;
        				}
      				};

      				selecionarRIPsCtrl.marcarDesmarcarTodosCheckbox = function(){
      					if(selecionarRIPsCtrl.selecionarTodos){
      						angular.forEach(selecionarRIPsCtrl.resultadoPesquisa, function(item, key){
      							var isItemSelecionado = false;
      							angular.forEach(selecionarRIPsCtrl.RIPSSelecionados, function(itemSelecionado, keySelecionados){
      								if(itemSelecionado === item.rip){
      									isItemSelecionado = true;
      								}
      							});
      							if(!isItemSelecionado){
      								selecionarRIPsCtrl.RIPSSelecionados.push(item.rip);
      							}
      							item.isSelecionado = true;
      						});
      					}else{
      						selecionarRIPsCtrl.RIPSSelecionados = [];
      						angular.forEach(selecionarRIPsCtrl.resultadoPesquisa, function(item, key){
      							item.isSelecionado = false;
      						});
      					}
      				};

				}],
				templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/modals/selecionar-rips/dialog-selecionar-rips.html',
				parent: angular.element(document.body),
				clickOutsideToClose: false
			});
		}
		return {
			exibirSelecionarRIPs: exibirSelecionarRIPs
		};
	}
})();

( function () {
    "use strict";
    angular.module("su-cadastro-imovel").service("imovelService", ["UnidadeAutonomaService", "MensagensService", "CpfCnpjService", function(UnidadeAutonomaService, MensagensService, CpfCnpjService) {

        function Imovel() {

            return {
                numeroProcesso: null,
                usuarioCadastrador: 1,
                enderecamento: {},
                proprietario: {
                    tipoProprietarioReal: {}
                },
                proprietarioTitular: {
                    tipoProprietarioReal: {id: undefined, descricao: undefined}
                },
                unidadeAutonoma: UnidadeAutonomaService.novaUnidadeAutonoma(),
                incorporacao: {
                    matriculas: [],
                    certidoesCartoriais: []
                },
                terreno: {},
                titulos: [],
                benfeitorias: [],
                matriculas: [],
                ripsFusao: [],
                dsSpiunet: 'pend. sincronizacao',
                proprietariosTerceiros: [],
                proprietariosParciais: [],
                proprietariosCartorio: []
            };
        }

        function novoImovel() {
            return new Imovel();
        }

        /**
         * Verifica se a data da Matrícula é menor que a data do Registro. <br />
         * Caso seja verdade, a função lança uma mensagem e encerra o fluxo. <br />
         * Referência: US002 - Critério 31
         */
        function validaDataMatricula (matricula) {
            if (matricula.dataMatricula < matricula.dataRegistro) {
                var _mensagem = "A data da matrícula não pode ser anterior a data do registro.";
                MensagensService.exibirMensagemErro(_mensagem);
                throw _mensagem;
            }
        }

        this.calcDigitosPosicoes = function( digitos, posicoes, somaDigitos ) {

            posicoes = 10;
            somaDigitos = 0;
            digitos = digitos.toString();

            for ( var i = 0; i < digitos.length; i++  ) {

                somaDigitos = somaDigitos + ( digitos[i] * posicoes );
                posicoes--;
                if ( posicoes < 2 ) {
                    posicoes = 9;
                }
            }

            somaDigitos = somaDigitos % 11;
            if ( somaDigitos < 2 ) {
                somaDigitos = 0;
            } else {
                somaDigitos = 11 - somaDigitos;
            }

            var cpf = digitos + somaDigitos;

            return cpf;

        };

        function validaCpf (cpf) {            
            return CpfCnpjService.validaCpf(cpf);           
        }

        function validarCnpj(cnpj) {    
            return CpfCnpjService.validaCnpj(cnpj);            
        }

        return {
            novoImovel: novoImovel,
            validaDataMatricula: validaDataMatricula,
            validarCnpj: validarCnpj,
            validaCpf: validaCpf
        };
    }]);
})();

(function () {
    'use strict';
    angular
        .module('su-cadastro-imovel')
        .service('homologarEnviarDestinacaoService', ["$http", "urlBase", function ($http, urlBase) {
            this.homologarEnviarDestinacao = function (imovel) {
                return $http.post(urlBase + 'imovel/homologarEnviarDestinacao', imovel);
            };
            this.documentoSei = function (objeto) {
                return $http.post(urlBase + 'imovel/enviarDocumentosSei', objeto);
            };
        }])
        .service('obterTerrenosIntersedidos', ["$http", "urlBase", function ($http, urlBase) {
            this.listarTerrenosIntersedidos = function (terreno) {
                return $http.post(urlBase + 'imovel/buscarTerrenos', terreno);
            };
        }])
        .factory('Imovel', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/cadastro/:id', { id: '@id' }, {
                save: {
                    method: 'POST',
                    transformRequest: function(data, headersGetter) {
                        var payload = angular.copy(data);
                        
                        // Remove proprietario.id para evitar que backend recarregue do banco
                        if (payload.id && payload.proprietario && payload.proprietario.id) {
                            delete payload.proprietario.id;
                        }
                        
                        return angular.toJson(payload);
                    }
                }
            });
        }])
        .factory('Homologado', ["$resource", "urlBase", "$http", function ($resource, urlBase, $http) {
            var _resources = {
                homologar: $resource(
                    urlBase + 'imovel/homologado/:id', { id: '@id' }, {
                    save: { method: 'POST' },
                }
                ),
            };

            function recusar(imovelRecusado) {
                return $http.post(
                    urlBase + 'imovel/recusar-homologacao',
                    imovelRecusado
                );
            }

            return {
                save: function ($resource) {
                    return _resources.homologar.save($resource).$promise;
                },
                recusar: recusar,
            };
        }])

        .factory('Destinacao', ["$resource", "urlBase", function ($resource, urlBase) {
            var _resources = {
                enviar: $resource(
                    urlBase + 'comum/enviar-dados-destinacao/:id', { id: '@id' }, {
                    send: { method: 'POST' },
                }
                ),
            };

            return {
                send: function ($resource) {
                    return _resources.enviar.send($resource).$promise;
                },
            };
        }])
        .factory('ReceitaService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'receita/pessoa/porCpfCnpj/:cpfCnpj', {
                cpfCnpj: '@cpfCnpj',
            });
        }])
        .factory('CidadesExtService', ["$resource", "$http", "urlBase", function ($resource, $http, urlBase) {
            return $resource(urlBase + 'imovel/cidadeExterior/por-pais/:id_pais', {
                id_pais: '@id_pais',
            });
        }])
        .factory('RipsFusao', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/buscaRipsFusao/:id_imovel', {
                id_imovel: '@id_imovel',
            });
        }])
        .factory('ectService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'ect/porCep/:cep', { cep: '@cep' });
        }])
        .factory('ectCepService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'ect/localidade/porCep/:cep', { cep: '@cep' });
        }])
        .factory('ValidaPontoImovelService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/validaPontoImovel');
        }])
        .factory('CertidaoCartorial', ["$resource", "urlBase", function ($resource, urlBase) {
            function formDataObject(data) {
                var fd = new FormData();
                angular.forEach(data, function (value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + 'imovel/certidao-cartorial/upload', null, {
                save: {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {
                        'Content-Type': undefined,
                        enctype: 'multipart/form-data',
                    },
                },
            });
        }])
        .factory('PlantaUnidade', ["$resource", "urlBase", function ($resource, urlBase) {
            function formDataObject(data) {
                var fd = new FormData();
                angular.forEach(data, function (value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + 'imovel/planta-unidade/upload', null, {
                save: {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {
                        'Content-Type': undefined,
                        enctype: 'multipart/form-data',
                    },
                },
            });
        }])
        .factory('ImagemUpload', ["$resource", "urlBase", function ($resource, urlBase) {
            function formDataObject(data) {
                var fd = new FormData();
                angular.forEach(data, function (value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + 'imovel/galeria-imagem/upload', null, {
                save: {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {
                        'Content-Type': undefined,
                        enctype: 'multipart/form-data',
                    },
                },
            });
        }])

        .factory('ArquivoContratoUpload', ["$resource", "urlBase", function ($resource, urlBase) {
            function formDataObject(data) {
                var fd = new FormData();
                angular.forEach(data, function (value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + 'imovel/contrato/upload', null, {
                save: {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {
                        'Content-Type': undefined,
                        enctype: 'multipart/form-data',
                    },
                },
            });
        }])
        .factory('ExibirCertidao', ["$http", "urlBase", function ($http, urlBase) {
            function ab2str(arraybuffer) {
                var uarr = new Uint8Array(arraybuffer);
                var strings = [],
                    chunksize = 0xffff;
                var len = uarr.length;
                for (var i = 0; i * chunksize < len; i++) {
                    strings.push(
                        String.fromCharCode.apply(
                            null,
                            uarr.subarray(i * chunksize, (i + 1) * chunksize)
                        )
                    );
                }
                return strings.join('');
            }

            function setMime(string) {
                if (string.indexOf('PDF') !== -1) {
                    return 'application/pdf';
                } else if (string.indexOf('PNG') !== -1) {
                    return 'image/png';
                } else {
                    return 'image/jpg';
                }
            }

            return {
                visualizarCertidao: function (id) {
                    return $http({
                        url: 'http://' +
                            window.location.host +
                            urlBase +
                            'imovel/certidao-cartorial/listar',
                        method: 'GET',
                        responseType: 'arraybuffer',
                        params: { id: id },
                    }).then(function (data) {
                        var str = ab2str(data.data);
                        var file = new Blob([data.data], { type: setMime(str) });
                        var fileURL = URL.createObjectURL(file);
                        window.open(fileURL);
                    });
                },
            };
        }])
        .factory('ExibirPlantaUnidade', ["$http", "urlBase", function ($http, urlBase) {
            function ab2str(arraybuffer) {
                var uarr = new Uint8Array(arraybuffer);
                var strings = [],
                    chunksize = 0xffff;
                var len = uarr.length;
                for (var i = 0; i * chunksize < len; i++) {
                    strings.push(
                        String.fromCharCode.apply(
                            null,
                            uarr.subarray(i * chunksize, (i + 1) * chunksize)
                        )
                    );
                }
                return strings.join('');
            }

            function setMime(string) {
                if (string.indexOf('PDF') !== -1) {
                    return 'application/pdf';
                } else if (string.indexOf('PNG') !== -1) {
                    return 'image/png';
                } else {
                    return 'image/jpg';
                }
            }

            return {
                visualizarPlanta: function (id) {
                    return $http({
                        url: 'http://' +
                            window.location.host +
                            urlBase +
                            'imovel/planta-unidade/listar',
                        method: 'GET',
                        responseType: 'arraybuffer',
                        params: { id: id },
                    }).success(function (data) {
                        var str = ab2str(data);
                        var file = new Blob([data], { type: setMime(str) });
                        var fileURL = URL.createObjectURL(file);
                        window.open(fileURL);
                    });
                },
            };
        }])

        .factory('CancelarBenfeitoria', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(
                urlBase + 'imovel/cancelar-benfeitoria', { benfeitoria: '@benfeitoria' }, {
                save: {
                    method: 'POST',
                    params: {
                        benfeitoria: '@benfeitoria',
                    },
                },
            }
            );
        }])

        .factory('PontoImovel', ["$resource", "urlBase", function ($resource, urlBase) {
            var _resources = {
                homologar: $resource(
                    urlBase + 'imovel/ponto/validar', {}, {
                    save: { method: 'POST' },
                }
                ),
            };
            return {
                save: function ($resource) {
                    return _resources.homologar.save($resource).$promise;
                },
            };
        }])

        .factory('PesquisarImovelMatricula', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(
                urlBase +
                'imovel/pesquisar-imovel-matricula/:numeroMatricula/:idCartorio', { numeroMatricula: '@numeroMatricula', idCartorio: '@idCartorio' }, {
                getImovelMatricula: {
                    method: 'GET',
                    isArray: true,
                },
            }
            );
        }])

        .factory('ImovelModel', ["$resource", function ($resource) {
            var vm = this;
            vm.imovel = {};
            vm.dadosPonto = {};
            vm.point = null;

            return {
                imovel: vm.imovel,
                dadosPonto: vm.dadosPonto,
                point: vm.point,
            };
        }])

        .factory('TipoTerreno', ["$resource", function ($resource) {
            var tipoTerreno;

            function setTipoTerreno(tipoTerreno) {
                this.tipoTerreno = tipoTerreno;
            }

            function getTipoTerreno() {
                return this.tipoTerreno;
            }

            return {
                setTipoTerreno: setTipoTerreno,
                getTipoTerreno: getTipoTerreno,
            };
        }])
        .factory('PesquisaImovel', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(
                urlBase + 'imovel/pesquisar', {
                rip: '@rip',
                idPais: '@idPais',
                dataInicio: '@dataInicio',
                dataFim: '@dataFim',
                uf: '@uf',
                municipio: '@municipio',
                bairro: '@bairro',
                cidadeExterior: '@cidadeExterior',
                numeroPagina: '@numeroPagina',
                maximoPorPagina: '@maximoPorPagina',
                campoOrdenacao: '@campoOrdenacao',
            }, {
                query: {
                    method: 'GET',
                    params: {
                        rip: '@rip',
                        idPais: '@idPais',
                        dataInicio: '@dataInicio',
                        dataFim: '@dataFim',
                        uf: '@uf',
                        municipio: '@municipio',
                        bairro: '@bairro',
                        cidadeExterior: '@cidadeExterior',
                        numeroPagina: '@numeroPagina',
                        maximoPorPagina: '@maximoPorPagina',
                        campoOrdenacao: '@campoOrdenacao',
                    },
                },

                downloadFile: {
                    method: 'GET',
                    responseType: 'arraybuffer',
                    url: urlBase + 'imovel/pesquisa/relatorio',
                    params: {
                        rip: '@rip',
                        pais: '@pais',
                        dataInicio: '@dataInicio',
                        dataFim: '@dataFim',
                        uf: '@uf',
                        municipio: '@municipio',
                        bairro: '@bairro',
                        cidadeExterior: '@cidadeExterior',
                        tipo: '@tipo',
                    },
                },
            }
            );
        }])
        .factory('Documento', ["$resource", "urlBase", function ($resource, urlBase) {
            function formDataObject(data) {
                var fd = new FormData();
                angular.forEach(data, function (value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + 'imovel/documento/upload', null, {
                save: {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {
                        'Content-Type': undefined,
                        enctype: 'multipart/form-data',
                    },
                },
            });
        }])
        .factory('PesquisaImovelRelatorio', ["$http", "urlBase", function ($http, urlBase) {
            return {
                downloadFile: function (imovelPesquisa, tipo) {
                    return $http
                        .get(urlBase + 'imovel/pesquisa/relatorio', {
                            responseType: 'arraybuffer',
                            params: { imovelPesquisa: imovelPesquisa, tipo: tipo },
                        })
                        .then(function (response) {
                            return response;
                        });
                },
            };
        }])
        .factory('ValidaRipFracionamentoService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/rip/validar/:rip', { rip: '@rip' });
        }])
        .factory('ValidaRipFusaoService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/rip/validar/fusao/:rip', {
                rip: 'rip'
            });
        }])
        .factory('VerificarRestricaoService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/rip/restricao/:rip', {
                rip: 'rip'
            });
        }])
        .factory('ImovelTerrenoService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(
                urlBase + 'imovel/imoveis/:uuidTerreno', { uuidTerreno: 'uuidTerreno' }, {
                getImoveisByUuidTerrenoService: {
                    method: 'GET',
                    params: {
                        uuidTerreno: '@uuidTerreno',
                    },
                },
            }
            );
        }])
        .factory('ImovelPontoService', ["$resource", "urlBase", function ($resource, urlBase) {
          return $resource(
              urlBase + 'imovel/imoveis/ponto/:ponto', { ponto: 'ponto' }, {
              getImoveisByPontoService: {
                  method: 'GET',
                  params: {
                      ponto: '@ponto',
                  },
              },
          }
          );
      }])
        .factory('ImovelOverlayService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/imoveis-overlay/:uuid', {
                uuid: '@uuid',
            });
        }])

        .factory('HistoricoImovelService', ["$http", "urlBase", function ($http, urlBase) {
            return {
                getHistorico: function (uuid) {
                    return $http
                        .get(urlBase + 'historico/' + uuid)
                        .then(function (response) {
                            return response;
                        });
                },
            };
        }])
        .factory('SistemaService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/sistema/all');
        }])
        .factory('ImovelRecusadoService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/recusado/all');
        }])
        .factory('AcaoExecutasService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/acaoExecutada/all');
        }])
        .factory('CancelamentoService', ["$http", "urlBase", function ($http, urlBase) {
          return {
            getCancelamentoPorImovel: function (id) {
                return $http
                    .get(urlBase + 'imovel/get-cancelamento/' + id)
                    .then(function (response) {
                        return response;
                    });
            }
        };
        }])
        .factory('PendenciasService', ["$resource", "urlBase", function ($resource, urlBase) {
            function getResumoPendencias(tipoTerreno) {
                return $resource(urlBase + 'servico/buscaPendenciasIncorporacao');
            }

            function getPendenciaPorImovel(id) {
                return $resource(urlBase + 'imovel/pendencias/' + id);
            }

            return {
                getResumoPendencias: getResumoPendencias,
                getPendenciaPorImovel: getPendenciaPorImovel,
            };
        }]);
})();

(function(){
  angular.module('su-cadastro-imovel').factory('NotificadorService', ["$rootScope", function($rootScope) {


    return {
        subscribe: function(scope, callback, eventName) {
            var handler = $rootScope.$on(eventName, callback);
            scope.$on('$destroy', handler);
        },

        notify: function(scope,object, eventName) {
            $rootScope.$emit(eventName,scope,object);
        }

    };
}]);


})();

angular.module("su-cadastro-imovel")
	.service("tabsIncorporacao", function Tabs(){

		var vm = this;

        var tabResultadoHomologacao = {
            nome : "Resultado de Homologação",
            index : -2,
            template : "scripts-cadastro-imovel/pages/imovel/view/resultado-homologacao.html",
            edit : "scripts-cadastro-imovel/pages/imovel/view/resultado-homologacao.html",
            detail : "scripts-cadastro-imovel/pages/imovel/view/resultado-homologacao.html"
        };

		var tabLocalizacao = {
            nome : "Localização do Imóvel",
            index : -1,
            template : "scripts-cadastro-imovel/pages/imovel/view/inserir-localizacao.tmpl.html",
            edit : "scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-localizacao.tmpl.html",
            detail : "scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-localizacao-imovel.tmpl.html"
        };

		var tabDadosGerais = {
            nome : "Dados Gerais do Imóvel",
            index : 0,
            template : "scripts-cadastro-imovel/pages/imovel/view/inserir-imovel.tmpl.html",
            edit : "scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-imovel.html",
            detail : "scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-dados-gerais-imovel.tmpl.html"
        };

		var tabTerreno = {
            nome : "Terreno",
            index : 1,
            template : "scripts-cadastro-imovel/pages/terreno/view/inserir-terreno.tmpl.html",
            edit : "scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-terreno.html",
            detail : "scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-terreno-imovel.tmpl.html"
        };

        var tabBenfeitoria = {
            nome : "Acessão/Benfeitoria",
            index : 2,
            template : "scripts-cadastro-imovel/pages/benfeitoria/view/inserir-benfeitoria.tmpl.html",
            edit : "scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-benfeitoria.html",
            detail :  "scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-benfeitoria.tmpl.html"
        };

        var tabUnidadeAutonoma = {
            nome : "Unidade Autônoma",
            index : 3,
            template : "scripts-cadastro-imovel/pages/unidade-autonoma/view/inserir-unidade-autonoma.tmpl.html",
            edit : "scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-unidade-autonoma.html",
            detail : "scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-unidade-autonoma.tmpl.html"
        };

		vm.tabInclusaoBenfeitoria = {
            nome : "Incluir Dados da Benfeitoria",
            index : 4,
            template : "scripts-cadastro-imovel/pages/benfeitoria/view/incluir-dados-benfeitoria.html",
            edit : "scripts-cadastro-imovel/pages/benfeitoria/view/incluir-dados-benfeitoria.html",
            detail : "scripts-cadastro-imovel/pages/benfeitoria/view/incluir-dados-benfeitoria.html"
        };

        var tabProprietario = {
            nome : "Proprietário",
            index : 5,
            template : "scripts-cadastro-imovel/pages/imovel/view/proprietario.tmpl.html",
            edit : "scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-proprietario.html",
            detail : "scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-proprietario.tmpl.html"
        };

		vm.tabInclusaoTituloAquisitivo = {
            nome : "Título Aquisitivo",
            index : 6
        };

        vm.tabFormDocumento = {
            nome : "Documento",
            index : 7
        };

        var tabHistorico = {
            nome : "Histórico",
            index : 8,
            detail : "scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-historico.tmpl.html"
        };

        vm.tabs = [tabLocalizacao, tabDadosGerais, tabTerreno, tabProprietario, tabHistorico];
		vm.selectedIndex = -1;

		vm.selectTab = function(num){
			vm.selectedIndex = num;
		};

        vm.hasTabBenfeitoria = function () {
            return vm.tabs.indexOf(tabBenfeitoria) > -1;
        };

		vm.previousTab = function () {
		    var anterior;
		    for (var i = 0; i < vm.tabs.length; i++) {
                if (vm.tabs[i].index === vm.selectedIndex) {
                    anterior = vm.tabs[i-1].index;
                }
		    }
		    vm.selectedIndex = anterior;
        };

		vm.addTabResultadoHomologacao = function () {
            var index = vm.tabs.indexOf(tabResultadoHomologacao);
            if (index === -1) {
                vm.tabs.unshift(tabResultadoHomologacao);
                vm.selectedIndex = tabResultadoHomologacao.index;
            }
        };
        vm.removeTabResultadoHomologacao = function () {
            var index = vm.tabs.indexOf(tabResultadoHomologacao);
            if (index > -1) {
                vm.tabs.splice(index, 1);
                vm.selectedIndex = tabLocalizacao.index;
            }
        };
		vm.addTabBenfeitoria = function () {
            var index = vm.tabs.indexOf(tabBenfeitoria);
            if (index === -1) {
                vm.tabs.splice(3, 0, tabBenfeitoria);
            }
        };

		vm.removeTabBenfeitoria = function () {
		    var index = vm.tabs.indexOf(tabBenfeitoria);
		    if (index > -1) {
		        vm.tabs.splice(index, 1);
            }
        };

		vm.addTabUnidadeAutonoma = function () {
		    var index = vm.tabs.indexOf(tabUnidadeAutonoma);
		    if (index === -1) {
		        vm.tabs.splice(3, 0, tabUnidadeAutonoma);
            }
        };

		vm.removeTabUnidadeAutonoma = function () {
		    var index = vm.tabs.indexOf(tabUnidadeAutonoma);
		    if (index > -1) {
		        vm.tabs.splice(index, 1);
            }
        };

        vm.addTabHistorico = function () {
            var index = vm.tabs.indexOf(tabHistorico);
            if (index === -1) {
                vm.tabs.splice((vm.tabs).length, 0, tabHistorico);
            }
        };

        vm.removeTabHistorico = function () {
            var index = vm.tabs.indexOf(tabHistorico);
            if (index > -1) {
                vm.tabs.splice(index, 1);
            }
        };


		vm.atualizaTabs = function (tipoImovel) {
            if (tipoImovel && tipoImovel.id) {
                if (isUnidadeAutonoma(tipoImovel)) {
                    vm.removeTabBenfeitoria();
                    vm.addTabUnidadeAutonoma();
                } else if (isBenfeitoria(tipoImovel)) {
                    vm.removeTabUnidadeAutonoma();
                    vm.addTabBenfeitoria();
                } else {
                    vm.removeTabBenfeitoria();
                    vm.removeTabUnidadeAutonoma();
                }
            }
        };

		function isUnidadeAutonoma(tipoImovel) {
            return tipoImovel.tipoPai === "UNIDADE AUTÔNOMA";
        }

        function isBenfeitoria(tipoImovel) {
            return tipoImovel.id === 2 || tipoImovel.id === 3;
        }

	})

.service("fileService", function (){

	var service=this;

	function ab2str(arraybuffer){
		return String.fromCharCode.apply(null, new Uint8Array(arraybuffer));
	}

	function setMime(string){
		string=string.toUpperCase();
		if(string.indexOf("PDF") !== -1){
			return "application/pdf";
		}else if(string.indexOf("PNG") !== -1){
			return "image/png";
		}else{
			return "image/jpg";
		}
	}

	function stringToArray($string){
		var bytes = new Uint8Array($string.length);
        for (var i=0; i<$string.length; i++){
            bytes[i] = $string.charCodeAt(i);
        }
        return bytes;
	}

	service.convertStringToFile=function($file,$nomeArquivo){
		var bytes=stringToArray($file);
        var file =new Blob([bytes], {type: setMime($nomeArquivo)});
        var fileURL = URL.createObjectURL(file);
        window.open(fileURL);
	};

	service.convertArrayBufferToPdf=function($file){
        var file = new Blob([$file], {type: 'application/pdf'});
        var fileURL = URL.createObjectURL(file);
        window.open(fileURL);
	}

	service.convertArrayBufferToFile=function($file){
		var str = ab2str($file);
        var file = new Blob([str], {type: setMime(str)});
        var fileURL = URL.createObjectURL(file);
        window.open(fileURL);
	};

})

.service("verificaObrigatorios", ["$resource", function Tabs($resource){

	var imovel=[
	    {key:'numeroProcesso',nome:'Número do Processo'},
	    {key:'pais',nome:'Pais'},
		{key:'cep',nome:'CEP'},
		{key:'bairro',nome:'Bairro'},
		{key:'numero',nome:'Número'},
		{key:'tipoImovel',nome:'Tipo do Imovel'},
		{key:'tipoIncorporacao',nome:'Tipo da Incorporação'},
		{key:'descricaoOutraRestricao',nome:'Descrição da Outra Restrição'},
		{key:'areaUniaoCartorial',nome:'Área União Cartorial'},
		{key:'areaUniaoCadastral',nome:'Área União Cadastral'},
		{key:'fundamento',nome:'Fundamento'},
		{key:'situacaoProcessoDemarcatorio',nome:'Situação do Processo Demarcatório'},
		{key:'cartorioNome',nome:'Nome do Cartorio'},
		{key:'direitoAdquiridoIncorporado',nome:'Direito Adquirido/Incorporado'},
		{key:'numeroMatricula',nome:'Numero da Matricula'},
		{key:'dataMatricula',nome:'Data da Matricula'},
		{key:'dataRegistro',nome:'Data do Registro'},
		{key:'certidaoCartorial',nome:'Certidão Cartorial'},
		{key:'naturezaImovel',nome:'Natureza do Imovel'},
		{key:'tipoIlha',nome:'Tipo da Ilha'},
		{key:'pontoImovel',nome:'Localização Mapa'},
		{key:'geometriaTerreno',nome:'Área do Terreno'},
		{key:'memorialDescritivoCartorial',nome:'Memorial Descritivo Cartorial'},
		{key:'memorialDescritivoCadastral',nome:'Memorial Descritivo Cadastral'},
		{key:'areaTotalCartorial',nome:'Área Total Cartorial'},
		{key:'areaTotalCadastral',nome:'Área Total Cadastral'},
		{key:'nome',nome:'Nome'},
		{key:'dataEmissao',nome:'Data de Emissão'},
		{key:'arquivoCertidaoCartorial',nome:'Arquivo da Certidão Cartorial'},
		{key:'proprietarioTitular',nome:'Proprietário/Titular do direito real ou pessoal'},
		{key:'tipoEdificacao',nome:'Tipo de edificação'},
		{key:'tipoEstrutura',nome:'Tipo de Estrutura'},
		{key:'areaGaragem',nome:'Área da Garagem'},
		{key:'garagemMatricula', nome:'Garagem com Matricula'},
		{key:'fracaoIdealGaragem',nome:'Fração ideal'},
		{key:'tipoGaragem',nome:'Tipo de garagem'},
		{key:'areaConstruida',nome:'Área construída'},
		{key:'fracaoIdeal',nome:'Fração ideal'},
		{key:'numeroDormitorios',nome:'Número de dormitórios'},
		{key:'padraoAcabamento',nome:'Padrão de acabamento'},
		{key:'estadoConservacao',nome:'Estado de conservação'},
		{key:'tipoGaragemUA',nome:'Tipo de garagem'},
		{key:'numeroVagas',nome:'Quantidade de vagas'},
		{key:'tipoAcessaoBenfeitoria', nome:'Tipo de Acessão/Benfeitoria'},
		{key:'tipoEspecializacaoEdificacao', nome:'Especialização da Benfeitoria/Acessão'},
		{key:'tipoEspecializacaoComplementar', nome:'Especialização da Benfeitoria/Acessão'},
		{key:'areaProjecao', nome:'Área Projeção'},
		{key:'numeroPavimentos', nome:'Número de Pavimentos'},
		{key:'vagasSinalizadas', nome:'Há vagas próximas dos acessos de circulação de pedrestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente'},
		{key:'acessoLivreObstaculos', nome:'Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida'},
		{key:'banheiroAcessivel', nome:'Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida'},
		{key:'elevadorAcessivel', nome:'Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida'},
		{key:'semBenfeitoria', nome:'É necessário adicionar pelo menos uma benfeitoria antes de gravar.'},
		{key:'numeroUnidadesResidenciais', nome:"Número de unidades residenciais"},
		{key:'geometriaUnidadeAutonoma',nome:'Geometria da Edificação'},
		{key:'uf', nome:'Unidade Federativa'},
		{key:'tipoProprietario', nome:'Tipo Proprietário'},
		{key:'autarquiasProprietario', nome:'Autarquias e Fundações do Proprietário'},
		{key:'isIntegralmente', nome:'Proprietário Integral/Parcialmente'},
		{key:'proprietarioParcial', nome:'Proprietário Parcial'},
		{key:'tipoProprietarioTitular', nome:"Tipo Titular"},
		{key:'autarquiasFundacoesTitular', nome:'Autarquias e Fundações do Proprietário/Titular do direito real ou pessoal'},
		{key:'cnpjOrgao', nome:'CNPJ do Órgão'},
		{key:'entiExtinta', nome:'Entidade Extinta'},
		{key:'pessoasProprietarioAnterior', nome:'CPF/CNPJ Proprietários Anteriores'},
		{key:'vagasProximasAcessoCirculacao', nome:'Há vagas próximas dos acessos de circulação de pedestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente'},
		{key:'acessosLivresBarreirasArquitetonicas', nome:'Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida'},
		{key:'possuiElevador', nome:'Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.'},
		{key:'tipoUnidadeAutonoma', nome:'Tipo de Unidade Autônoma'},
		{key:'autarquiaFundacaoParcial', nome:'Autarquias e Fundações do Proprietário Parcial'},
		{key:'terrenoAcessaoBenfeitoria', nome:'Terreno com Acessão/Benfeitoria'},
		{key:'tipoAquisicao', nome:'Tipo de Aquisição'},
		{key:'planoProtecaoIncendioFile', nome:'Arquivo de Plano de Protecao Contra Incendio'},
		{key:'habitese', nome:'Arquivo de Habite-se'},
		{key:'alvaraConstrucaoFile', nome:'Arquivo de Alvará de Construção'},
		{key:'laudoPredialFile', nome:'Arquivo de Laudo de Inspenção Predial'},
		{key:'outroDescricaoFile', nome:'Arquivo de Outro'},
		{key:'pessoasProprietario', nome:'CPF/CNPJ Proprietário'},
		{key:'especializacaoEspelhoDagua', nome:'Especialização Espelho D'+"'"+'água'},
		{key:'tipoContrato', nome:'Tipo de Contrato Pessoal'},
		{key:'ano',nome:'Ano Habite-se'},
		{key:'fotoDescricao',nome:'Descrição'},
		{key:'fotoData',nome:'Data da Foto'},
		{key:'fotoImagem',nome:'Foto Arquivo'},
		{key:'nomePlantaUnidade',nome:'Nome'},
		{key:'dataEmissaoPlantaUnidade',nome:'Data'},
		{key:'plantaUnidade',nome:'Arquivo da Planta'},
		{key:'dataDocumento',nome:'Data do Documento'},
		{key:'arquivo', nome:'Arquivo'},
		{key:'shapefileNaoCarregado', nome:'A geometria do arquivo shapefile não foi carregada'},
		{key:'foto', nome:'Arquivo Foto'},
		{key:'justificativa', nome:'Justificativa'},
		{key:'motivoCancelamento', nome:'Motivo'},
		{key:'descricao', nome:'Descrição'},
		{key:'tipoEnce', nome:'Tipo da ENCE'},
		{key:'tipoEnceParcial', nome:'Tipo da ENCE Parcial'},
		{key:'nivelEnce', nome:'Nível da ENCE'},
		{key:'cidadeExterior', nome:'Cidade exterior'},
		{key:'cidadeExteriorText', nome:'Cidade exterior'},
		{key:'endereco', nome:'Endereço'}
	];

 	var imovelSuspensao=[
 		{key:'tipo',nome:'Tipo'},
 		{key:'numeroProcesso',nome:'Numero do Processo'},
 		{key:'descricao',nome:'Descrição'},
 		{key:'motivacao',nome:'Motivação'},
 		{key:'vigencia',nome:'Vigência'},
 		{key:'rip',nome:'RIP'},
 		{key:'restricao',nome:'Restrição'}
 	];

 	var getChavesByForm = function($form){
		var chaves=[];
		angular.forEach($form.$error.required, function(value, key) {
             if(value.$name && value.$name!=''){
                 chaves.push(value.$name);
             }
        });
        return chaves;
	};

	return {
		getImovel:function(){
			return imovel;
		},
		getImovelSuspensao:function(){
			return imovelSuspensao;
		},
		getValoresByChaves:function(chaves){
			var valores = [];
			angular.forEach(chaves, function(value, key){
				angular.forEach(imovel, function(valueImovel, keyImovel){
					if(value === valueImovel.key){
						valores.push(valueImovel.nome);
					}
				});
			});
			return valores;
		},
		getValoresByForm:function($form,$tipo){
			var errorsForms=[];
			var imoveisObrigatorios=[];
			if($tipo=='imovel'){
				imoveisObrigatorios=imovel;
			}else if($tipo=='imovelSuspensao'){
				imoveisObrigatorios=imovelSuspensao;
			}
            $obrigatorios=getChavesByForm($form);
            for (var j = 0; j < $obrigatorios.length; j++) {
                for (var i = 0; i < imoveisObrigatorios.length; i++) {
                    if($obrigatorios[j]==imoveisObrigatorios[i].key){
                        errorsForms.push(imoveisObrigatorios[i].nome);
                    }else if(i-1==imoveisObrigatorios.length){
                        errorsForms.push(imoveisObrigatorios[i].key);
                    }
                }
            }
            return errorsForms;
		},
		getTemplate:function($erros){
			return '<div>'+
				'Por favor, preencher os campos obrigatórios.'+
            	'<ul>'+
				'<li ng-repeat="error in '+$erros+' track by $index">'+
					'{{error}}'+
				'</li>'+
				'</ul>'+
            '</div>';
		},
		getController:function(){
			return function($scope,$mdDialog,errorsForms){
                    $scope.fechar=function(){
                        $mdDialog.hide();
                    };
                    $scope.errorsForms=errorsForms;
                };
		}
	}

}]);

(function () {
'use strict';
angular.module('su-cadastro-imovel')

    .factory("GerarFolhaDeRosto", ["$resource", "urlBase", function($resource, urlBase){
        return $resource(urlBase + 'imovel/folha-rosto/:id', {id : '@id'}, {
            getFile: {
                method: 'GET',
                headers: {
                    contentType: 'application/pdf'
                },
                responseType: 'arraybuffer',
                cache: false,
                transformResponse: function (data) {
                    return {
                        response: new Blob([data], {type: 'application/pdf'})
                    };
                }
            }
        });
    }])
})();

(function () {
    "use strict";
angular.module('su-cadastro-imovel')
  .factory('Dominio', ["$resource", "urlBase", function($resource, urlBase){
    var Dominio = {};
    Dominio.Constantes = {};
    Dominio.Mocks = {};
    Dominio.Constantes.OrigalmenteUniao = 1;
    Dominio.Constantes.tipoTerrenoCartorial = 0;
    Dominio.Constantes.tipoTerrenoCadastral = 1;
    Dominio.Constantes.tabLocalizacaoImovel = -1;
    Dominio.Constantes.tabDadosGeraisImovel = 0;
    Dominio.Constantes.tabTerreno = 1;
    Dominio.Constantes.tabBenfeitoria = 2;
    Dominio.Constantes.tabUnidadeAutonoma = 3;
    Dominio.Constantes.Propriedade = {id: 1, descricao: 'Propriedade'};
    Dominio.Constantes.EspelhoAgua = { id: 3, descricao: 'Espelho d\'água'};
    Dominio.Constantes.Cavidade = {id: 5, descricao: 'Cavidade Natural Subterrênea'};
    Dominio.Constantes.CpfCnpj = {id:2 , descricao: 'Ente Privado'};
    Dominio.Constantes.EntidadeFederal = {id:3 , descricao: 'Entidade Federal'};
    Dominio.Constantes.AutarquiaFundacao = {id:5, descricao: 'Autarquia/Fundação'};
    Dominio.Constantes.AutarquiaFundacaoFederal = {id:7, descricao: 'Autarquia/Fundação Federal'};
    Dominio.Constantes.Terceiros = {id:6, descricao: 'Terceiros'};
    Dominio.Constantes.EnteEstadual = {id:2 , descricao: 'Estadual'};
    Dominio.Constantes.EnteMunicipal = {id:3 , descricao: 'Municipal'};
    Dominio.Constantes.Brasil = { id: 1, descricao: 'Brasil'};
    Dominio.Constantes.TiposDeDesenho = {
        NAO_GEORREFERENCIADA: 'NAO_GEORREFERENCIADA',
        VETORIZACAO: 'VETORIZACAO',
        ARQUIVO: 'ARQUIVO'
    };

    Dominio.Mocks.orgaosEntidades = function () {
      return [

        {
          nome:'Departamento Nacional de Infraestrutura de Transportes - DNIT'
        },
        {
          nome:'Instituto Nacional do Seguro Social - INSS'
        },
        {
            nome:'Ministerio da Defesa'
        },
        {
          nome:'Ministério da Gestão e da Inovação em Serviços Públicos'
        },
        {
          nome: 'Receita Federal do Brasil - RFB'
        },
        {
          nome:'Senado Federal'
        },
        {
          nome:'Tribunal de Contas da União'
        },
        {
          nome:'Universidade de Brasília - UnB'
          }

      ]
    };

      Dominio.Mocks.ugsMinisterio = function () {
          return [
              {
                  id: 2071,
                  nome: '3 BATALHAO DE COMUNICACOES'
              },
              {
                  id: 2072,
                 nome: '3 BATALHAO DE POLICIA DO EXERCITO'
              },
              {
                  id: 2073,
                  nome: '3 INSPETORIA DE CONTABILIDADE E FINANCAS'
              },
              {
                  id: 2074,
                  nome: '3 REGIMENTO DE CAVALARIA DE GUARDA'
              }

          ]
      };

      Dominio.Mocks.motivoCancelamento = function () {
        return [
            {id: 1, descricao: 'Erro de Cadastro'},
            {id: 2, descricao: 'Duplicidade de Cadastro'},
            {id: 4, descricao: 'Decisão Judicial'},
            {id: 5, descricao: 'Fracionamento'},
            {id: 6, descricao: 'Unificação'},
            {id: 7, descricao: 'Alienação'},
            {id: 8, descricao: 'Dados Inconsistentes'},
            {id: 9, descricao: 'Imóvel não pertence a União - Alodial'},
            {id: 10, descricao: 'Transferência de Domínio'},
            {id: 11, descricao: 'Mudança Jurisdição Município'},
            {id: 12, descricao: 'Regularização da Base'},
            {id: 13, descricao: 'Permuta'},
            {id: 14, descricao: 'Regularização de Base - Portaria 703/2014 SPU/STN'},
            {id: 15, descricao: 'Encerramento de Contrato de Locação'},
            {id: 16, descricao: 'Reversão'}
        ]
    };

      Dominio.Mocks.ugsSenado = function () {
          return [
              {
                  id:64,
                  nome: 'SENADO FEDERAL'
              },
              {
                  id:67,
                  nome:'ILB - PROGRAMA INTERLEGIS'
              },
              {
                  id:68,
                  nome: 'PROJETO BID 1864 - SENADO'
              }

          ]
      };

      Dominio.Mocks.ugsTcu = function () {
          return [
              {
                  id:69,
                  nome:'TRIBUNAL DE CONTAS DA UNIAO'
              },
              {
                  id:70,
                  nome: 'SECRETARIA DE CONTROLE EXTERNO NO PARA'
              }

          ]
      };



      var _resources={
      tipoParticipanteParcial:$resource(urlBase + 'imovel/consulta/tipoParticipanteParcial', {},
        {
          find:{method:"GET",isArray:true}
        })
    };



    // EXISTE UM COMENTÁRIO NA PLANILHA DE DADOS INDICANDO PENDÊNCIA DOS VALORES
    Dominio.situacoesProcessoDemarcatorio = function() {
      return [{id:1 , descricao: 'Estudos de Identificação'},{id:2 , descricao: 'Aprovação do relatório'},{id:3 , descricao: 'Contraditório'},{id:4 , descricao: 'Encaminhamento do processo adminstrativo de demarcação'},
        {id:5 , descricao: 'Decisão do Ministério da Justiça'}, {id:6 , descricao: 'Homologação'}, {id:7 , descricao: 'Registro'}];
    };
    Dominio.tiposProprietarioReal = function () {
      return [{id:1 , descricao: 'União'}, Dominio.Constantes.AutarquiaFundacao, Dominio.Constantes.Terceiros];
    };

    Dominio.tiposTitular = function (){
      return [{id:1 , descricao: 'União'},Dominio.Constantes.AutarquiaFundacao];
    }

    Dominio.tiposProprietarioParcial = function () {
      return _resources.tipoParticipanteParcial.find().$promise;
    };
    Dominio.tiposProprietarioAnterior = function () {
      return [{id:1 , descricao: 'União'},{id:2 , descricao: 'Autarquia/Fundação'},
        {id:3 , descricao: 'Terceiros'}];
    };
    Dominio.tiposProprietarioOutorgante = function () {
      return [{id:3 , descricao: 'Ente Público'},
        {id:2 , descricao: 'Ente Privado'}];
    };
    Dominio.tiposEntesPublicosAnterior = function () {
      return [Dominio.Constantes.EnteEstadual,
        Dominio.Constantes.EnteMunicipal];
    };
    Dominio.tiposEntesPublicos = function () {
      return [{id:1 , descricao: 'Federal'},Dominio.Constantes.EnteEstadual,
        Dominio.Constantes.EnteMunicipal];
    };
    Dominio.tiposAdministracao = function () {
      return [{id:1 , descricao: 'Direta'},{id:2 , descricao: 'Indireta'}];
    };
    return Dominio;
  }])
  .factory('MunicipioService', ["$resource", "urlBase", function ($resource, urlBase) {
    return{
      TodosMunicipios : $resource(urlBase + 'dominio/municipio/porUf/:uf',{uf: "@uf"}, {'query': { method: 'GET', isArray: true }}),
      ApenasMunicipios : $resource(urlBase + 'dominio/municipioApenas/porUf/:uf',{uf: "@uf"}, {'query': { method: 'GET', isArray: true }}),
      Municipio : $resource(urlBase + 'dominio/municipio/findOne/:uf/:municipio',{uf:"@uf",municipio:"@municipio"}, {'query': { method: 'GET', isArray: false }})
    };
  }])
  .factory('CartorioService', ["$resource", "urlBase", function ($resource, urlBase) {
    return{
      pesquisaCartorioPorId : $resource(urlBase + 'dominio/cartorio/findOne/:id',{id:"@id"}, {'query': { method: 'GET', isArray: false }}),
      pesquisaCartoriosPorUF : $resource(urlBase + 'dominio/cartorio/porMunicipio/:uf/:municipio',{uf:"@uf",municipio:"@municipio"}, {'query': { method: 'GET', isArray: true }})
    };
  }])
  .factory('AutarquiasService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/autarquias/all');
  }])
  .factory('ExtintasService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/extintas/all');
  }])
  .factory('EstataisService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/estatais/all');
  }])
  .factory('UfsService', ["$resource", "$http", "urlBase", function ($resource,$http, urlBase) {
    return {
      UFS : [{sigla:"AC",descricao:"Acre",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"AL",descricao:"Alagoas",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"AM",descricao:"Amazonas",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"AP",descricao:"Amapá",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"BA",descricao:"Bahia",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"CE",descricao:"Ceará",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"DF",descricao:"Distrito Federal",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"ES",descricao:"Espírito Santo",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"GO",descricao:"Goiás",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"MA",descricao:"Maranhão",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"MG",descricao:"Minas Gerais",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"MS",descricao:"Mato Grosso do Sul",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"MT",descricao:"Mato Grosso",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"PA",descricao:"Pará",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"PB",descricao:"Paraíba",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"PE",descricao:"Pernambuco",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"PI",descricao:"Piauí",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"PR",descricao:"Paraná",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"RJ",descricao:"Rio de Janeiro",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"RN",descricao:"Rio Grande do Norte",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"RO",descricao:"Rondônia",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"RR",descricao:"Roraima",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"RS",descricao:"Rio Grande do Sul",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"SC",descricao:"Santa Catarina",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"SE",descricao:"Sergipe",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"SP",descricao:"São Paulo",ativo:null,dataCriacao:null,dataAtualizacao:null},{sigla:"TO",descricao:"Tocantins",ativo:null,dataCriacao:null,dataAtualizacao:null}],
      RecuperaUF : $resource(urlBase + 'dominio/ufs/findOne/:sigla',{sigla:"@sigla"}, {'query': { method: 'GET', isArray: true }})
    };
  }])    .factory('TipoFracionamentoService', ["$resource", "urlBase", function ($resource, urlBase) {
  return $resource(urlBase + 'dominio/fracionamento/all');
}])
  .factory('TipoIncorporacaoService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/incorporacao/all');
  }])
  .factory('FundamentoService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/fundamento/all');
  }])
  .factory('SituacaoIncorporacaoService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/situacao-incorporacao/all');
  }])
  .factory('DireitoIncorporadoService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/direito-incorporado/all');
  }])
  .factory('TipoAquisicaoService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-aquisicao/all');
  }])
  .factory('TipoContratoPessoalService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/contrato-pessoal/all');
  }])
  .factory('TipoUnidadeAutonomaService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/unidade-autonoma/all');
  }])
  .factory('TipoBenfeitoriaService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-benfeitoria/all');
  }])
  .factory('TipoEspecializacaoEdificacaoService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-especializacao-edificacao/all');
  }])
  .factory('TipoEspecializacaoComplementarService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-especializacao-complementar/all');
  }])
  .factory('MaterialConstrucaoService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-material-construcao/all');
  }])
  .factory('EstadoConservacaoService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/estado-conservacao/all');
  }])
  .factory('TipoEstruturaService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-estrutura/all');
  }])
  .factory("LogradouroService", ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/logradouros/all');
  }])
  .factory('EspecializacaoEspelhoDaguaService', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/especializacao-espelho-dagua/all');
  }])
  .factory("TipoEquipamento", ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-equipamento/all', null, {'query':  { method:'GET', cache:true, isArray:true}});
  }])
  .factory("PadraoAcessibilidade", ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/padrao-acessibilidade/all');
  }])
  .factory('FormaLevantamento', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-forma-levantamento/all', null, {'query':  { method:'GET', cache:true, isArray:true}});
  }])
  .factory('TipoAcabamento', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-acabamento/all');
  }])
  .factory('TipoEdificacao', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-edificacao/all');
  }])
  .factory('SituacaoCadastro', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/situacao-cadastro/all');
  }])
  .factory('TipoProprietarioReal', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-proprietario-real/all');
  }])
  .factory('OrgaosEntidadesMock', ["$resource", function($resource) {
    return $resource('scripts-cadastro-imovel/pages/imovel/service/mock/orgaos/orgaos.json');
  }])
  .factory('UgsSenadoMock', ["$resource", function($resource) {
    return $resource('scripts-cadastro-imovel/pages/imovel/service/mock/ugs/senado.json');
  }])
  .factory('UgsTcuMock', ["$resource", function($resource) {
    return $resource('scripts-cadastro-imovel/pages/imovel/service/mock/ugs/tcu.json');
  }])
  .factory('UgsMinisterioDefesaMock', ["$resource", function($resource) {
    return $resource('scripts-cadastro-imovel/pages/imovel/service/mock/ugs/ministerio.json');
  }])
  .factory('TipoTituloAquisitivoService', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-titulo-aquisitivo/all');
  }])
  .factory('TipoAtoLegalService', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-ato-legal/all');
  }])
  .factory('MoedaService', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/moeda/all');
  }])
  .factory('TipoProjetoService', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-projeto/all');
  }])
  .factory('TipoGaragemService', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/tipo-garagem/all');
  }])
  .factory('NaturezaImovelService', ["$resource", "urlBase", function($resource, urlBase) {
    return $resource(urlBase + 'dominio/naturezaImovel/all');
  }])
    .factory('RipLegadoService', ["$resource", "urlBase", function ($resource, urlBase) {
        return $resource(urlBase + 'dominio/ripLegadoService/:name');
  }])
  .factory('EmpresaFornecedoraService', ["$resource", "urlBase", function($resource, urlBase) {
      return{
          consultarEmpresaTipoEnergia: $resource(urlBase + 'dominio/empresa-fornecedora/consultarEmpresaTipoEnergia/:tipoFornecedor/:uf',{uf : "@uf"}, {tipoFornecedor:"@tipoFornecedor"}, {'query': { method: 'GET', isArray: true }}),
          consultarEmpresaTipoAgua: $resource(urlBase + 'dominio/empresa-fornecedora/consultarEmpresaTipoAgua/:tipoFornecedor/:uf',{uf : "@uf"}, {tipoFornecedor:"@tipoFornecedor"}, {'query': { method: 'GET', isArray: true }})
    };
  }])
  .factory('PaisesService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/paises/all');
  }])
  .factory('PaisesUtilizadosService', ["$resource", "urlBase", function ($resource, urlBase) {
    return $resource(urlBase + 'dominio/paises/utilizados');
  }])
})();

(function () {
'use strict';
angular.module('su-cadastro-imovel')

	.factory("DetalharImovel", ["$resource", "urlBase", function($resource, urlBase){
		return $resource(urlBase + 'imovel/detalhar/:id', {id : '@id'}, {
			get : {
				method: 'GET',
				transformRequest: function (data) {
                    return angular.toJson(data);
                }
			}
		});
	}])

	.factory("DetalharImagensImovel", ["$resource", "urlBase", function($resource, urlBase){
		return $resource(urlBase + 'imovel/lista-imagens/:idImovel', {idImovel : '@idImovel',
			query: {method: 'get', isArray: false}
		})
	}])

	.factory("DeletarImagemDetalhe", ["$resource", "urlBase", function($resource, urlBase){
		return $resource(urlBase + 'imovel/deletar-imagem/:idImovel/:idImagem',{idImovel : '@idImovel', idImagem : '@idImagem'})
	}]);
})();

(function () {
  'use strict';
  angular.module('su-cadastro-imovel').service('cpfCnpjMaskService', function () {
    
        
    this.cleanMask = function (cpfCnpj) {
        return cpfCnpj.replace(/[^A-Z0-9]/g,'');
    }

    this.getMask = function (cpfCnpj) {
        var cpfCnpjLimpo = this.cleanMask(cpfCnpj);        
        if (cpfCnpjLimpo.length <= 11) {
            return this.getMaskCpf(cpfCnpjLimpo);
        } else {
            return this.getMaskCnpj(cpfCnpjLimpo);
        }        
    };

    this.descartarCaracteresInvalidos = function (cpfCnpj) {
        const regexCaracteresPermitidos = /[A-Z0-9]/;
        var lastDigit = cpfCnpj[cpfCnpj.length - 1];
        if (!regexCaracteresPermitidos.test(lastDigit)) {                    
            cpfCnpj = cpfCnpj.substring(0, cpfCnpj.length - 1);
        }
        return cpfCnpj;
    }

    this.getMaskCpf= function (cpf) {
        
        var tamanho = cpf.length;

        if (tamanho === 4) {
            cpf = cpf.replace(/^([A-Z0-9]{3})([A-Z0-9]{1})/, "$1.$2")                    

        } else if (tamanho === 7) {                                        
            cpf = cpf.replace(/^([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{1})/, "$1.$2.$3")

        } else if (tamanho === 10) {                    
            cpf = cpf.replace(/^([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{1})/, "$1.$2.$3-$4")

        } else if (tamanho === 11) {
            cpf = cpf.replace(/^([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{2})/, "$1.$2.$3-$4")
        }

        return this.descartarCaracteresInvalidos(cpf);

    }

    this.getMaskCnpj = function (cnpj) {

        var tamanho = cnpj.length;

        if (tamanho === 3) {     
            cnpj = cnpj.replace(/^([A-Z0-9]{2})([A-Z0-9]{1})/, "$1.$2")
        } else if (tamanho === 6) {
            cnpj = cnpj.replace(/^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{1})/, "$1.$2.$3")
        } else if (tamanho === 9) {
            cnpj = cnpj.replace(/^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{1})/, "$1.$2.$3/$4")
        } else if (tamanho === 10) {     
            cnpj = cnpj.replace(/^(A-Z0-9{2})(A-Z0-9{3})(A-Z0-9{4})(A-Z0-9{1})/, "$1.$2.$3/$4")                                   
        } else if (tamanho === 11) {      
            cnpj = cnpj.replace(/^(A-Z0-9{2})(A-Z0-9{3})(A-Z0-9{3})(A-Z0-9{3})/, "$1.$2.$2/$4")                                      
        } else if (tamanho === 12) {                                        
            cnpj = cnpj.replace(/^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})/, "$1.$2.$3/$4")
        } else if (tamanho === 13) {
            cnpj = cnpj.replace(/^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})([A-Z0-9]{1})/, "$1.$2.$3/$4-$5")
        } else if (tamanho === 14) {
            cnpj = cnpj.replace(/^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})([A-Z0-9]{2})/, "$1.$2.$3/$4-$5")
        }

        return this.descartarCaracteresInvalidos(cnpj);

    }

  });
})();

(function () {
  'use strict';
  angular
    .module('su-cadastro-imovel')

    .factory('ImovelPesquisa', ["$resource", "urlBase", function ($resource, urlBase) {
      return $resource(
        urlBase + 'imovel/consultar',
        {
          rip: '@rip',
          idSituacaoImovel: '@situacaoImovel',
          dataInicio: '@dataInicio',
          dataFim: '@dataFim',
          idProprietario: '@proprietarioTitular',
          idPais: '@pais',
          cep: '@cep',
          uf: '@uf',
          municipio: '@municipio',
          numeroPagina: '@page',
          maximoPorPagina: '@max',
          cidadeExterior: '@cidadeExterior',
          numeroProcesso: '@numeroProcesso',
          matricula: '@matricula',
          cpfUsuario: '@cpfUsuario',
          cpfCnpjProprietarios: '@cpfCnpjProprietarios',
          acaoExecutada: '@acaoExecutada'
        },
        {
          query: {
            method: 'GET',
            params: {
              rip: '@rip',
              idSituacaoImovel: '@situacaoImovel',
              dataInicio: '@dataInicio',
              dataFim: '@dataFim',
              idProprietario: '@proprietarioTitular',
              idPais: '@pais',
              cep: '@cep',
              uf: '@uf',
              municipio: '@municipio',
              numeroProcesso: '@numeroProcesso',
              matricula: '@matricula',
              cpfUsuario: '@cpfUsuario',
              cpfCnpjProprietarios: '@cpfCnpjProprietarios',
              page: '@page',
              maximoPorPagina: '@max',
              cidadeExterior: '@cidadeExterior',
            }
          },

          downloadFile: {
            method: 'GET',
            responseType: 'arraybuffer',
            url: urlBase + 'imovel/consultar/relatorioJasper',
            params: {
              rip: '@rip',
              idSituacaoImovel: '@situacaoImovel',
              dataInicio: '@dataInicio',
              dataFim: '@dataFim',
              idProprietario: '@proprietarioTitular',
              idPais: '@pais',
              cep: '@cep',
              uf: '@uf',
              municipio: '@municipio',
              numeroProcesso: '@numeroProcesso',
              matricula: '@matricula',
              cpfUsuario: '@cpfUsuario',
              cpfCnpjProprietarios: '@cpfCnpjProprietarios',
              page: '@page',
              maximoPorPagina: '@max',
              cidadeExterior: '@cidadeExterior'
            },
            transformResponse: function (data) {
              return {
                  response: new Blob([data], {type: 'application/xls'})
              };
          }
        }
      }
      );
    }])
    .factory('SuspensaoPesquisa', ["$http", "urlBase", function ($http, urlBase) {
      function consultar(pesquisa, page, limit) {
        var parametros = {
          tipo: pesquisa.tipo,
          situacao: pesquisa.situacao,
          numeroProcesso: pesquisa.numeroProcesso,
          rip: pesquisa.rip,
          page: page,
          limit: limit,
        };

        return $http.get(urlBase + 'imovel/suspensao/consulta', {
          params: parametros,
        });
      }

      return {
        consultar: consultar,
      };
    }])
    .factory('TipoCancelImovelPesquisa', ["$resource", "urlBase", function ($resource, urlBase) {
      return $resource(urlBase + 'dominio/tipo-cancelamento-imovel/all');
    }])
    .factory('SituacaoImovel', ["$resource", "urlBase", function ($resource, urlBase) {
      return $resource(urlBase + 'dominio/situacao-imovel/all');
    }])
    .factory('Cancelamento', ["$resource", "urlBase", function ($resource, urlBase) {
      return $resource(urlBase + 'imovel/cancelamento-imovel/', {
        cancelamentoImovel: '@cancelamentoImovel',
      });
    }])
    .factory('TipoSuspensao', ["$resource", "urlBase", function ($resource, urlBase) {
      return $resource(urlBase + 'dominio/tipo-suspensao-imovel/all');
    }])
    .factory('VerificaDestinacao', ["$http", "urlBase", function ($http, urlBase) {
      return {
        test: function (imovel) {
          return $http
            .post(urlBase + 'imovel/verifica-destinacao', imovel)
            .then(
              function success(response) {
                return response;
              },
              function error(error) {
                return error;
              }
            );
        },
      };
    }])
    .factory('VerificaUtilizacao', ["$http", "urlBase", function ($http, urlBase) {
      return {
        test: function (imovel) {
          return $http
            .post(urlBase + 'imovel/verifica-utilizacao', imovel)
            .then(
              function success(response) {
                return response;
              },
              function error(error) {
                return error;
              }
            );
        },
      };
    }])
    .factory('VerificarEdicao', ["$http", "urlBase", function ($http, urlBase) {
      return {
        verificar: function (ripImovel) {
          return $http
            .get(urlBase + 'imovel/verificarEdicao/'+ripImovel)
            .then(
              function success(response) {
                return response.data;
              },
              function error(error) {
                return error;
              }
            );
        },
      };
    }]);
    ;
})();

(function () {
  'use strict';
  angular.module('su-cadastro-imovel').service('buscarCpfCnpjService', ["$http", "urlBase", function ($http, urlBase) {
    this.buscarPessoa = function (cpf) {
      return $http.get(urlBase + 'dados-pessoa/buscarcpf/' + cpf);
    };
    this.buscarCnpj = function (cnpj) {
      return $http.get(urlBase + 'dados-pessoa/buscarcnpj/' + cnpj);
    };
    this.buscarAutarquia = function (ugCnp) {
      return $http.get(urlBase + 'comum/ug/' + ugCnp);
    };
    this.buscarUgs = function () {
      return $http.get(urlBase + 'comum/ug/', { cache: true });
    };

  }]);
})();

(function () {
    "use strict";
    angular.module("su-cadastro-imovel")
        .factory('CartografiaServiceInc', ["$http", "urlBase", "UfsService", function ($http, urlBase, UfsService) {
            function transformarGeometria(sistemaReferenciaEntrada, sistemaReferenciaSaida, coordenadaX, coordenadaY) {
                var parametros = {
                    sistemaReferenciaEntrada: sistemaReferenciaEntrada,
                    sistemaReferenciaSaida: sistemaReferenciaSaida,
                    geometriaPonto: {
                        type: 'Point',
                        coordinates: [
                            coordenadaX,
                            coordenadaY
                        ]
                    }
                };
                return $http.post(urlBase + 'cartografia/geometria/transforma', parametros);
            }

            function gdToUtm(gd) {
                var lat = parseFloat(gd.latitude.decimal);
                var lng = parseFloat(gd.longitude.decimal);
                var k0 = 0.9996; //scale on central meridian
                var a = 6378137.0; //equatorial radius, meters.
                var f = 0.00335281066; //polar flattening.
                var b = a*(1-f); //polar axis.
                var e = Math.sqrt(1 - (b/a)*(b/a)); //eccentricity
                var drad = Math.PI/180; //Convert degrees to radians

                var phi = lat * drad; //Convert latitude to radians
                var utmz = 1 + Math.floor((lng+180)/6); //calculate utm zone
                var zcm = 3 + 6*(utmz-1) - 180; //Central meridian of zone

                // Calculate Intermediate Terms
                var esq = (1 - (b/a)*(b/a)); //e squared for use in expansions
                var e0sq = e*e/(1-e*e); // e0 squared - always even powers
                var N = a/Math.sqrt(1-Math.pow(e*Math.sin(phi),2));
                var T = Math.pow(Math.tan(phi),2);
                var C = e0sq*Math.pow(Math.cos(phi),2);
                var A = (lng-zcm)*drad*Math.cos(phi);

                // Calculate M
                var M = phi*(1 - esq*(1/4 + esq*(3/64 + 5*esq/256)));
                M = M - Math.sin(2*phi)*(esq*(3/8 + esq*(3/32 + 45*esq/1024)));
                M = M + Math.sin(4*phi)*(esq*esq*(15/256 + esq*45/1024));
                M = M - Math.sin(6*phi)*(esq*esq*esq*(35/3072));
                M = M*a; //Arc length along standard meridian

                // Calculate UTM Values
                var x = (k0*N*A*(1 + A*A*((1-T+C)/6 + A*A*(5 - 18*T + T*T + 72*C -58*e0sq)/120))) + 500000;
                var y = k0*(M + N*Math.tan(phi)*(A*A*(1/2 + A*A*((5 - T + 9*C + 4*C*C)/24 + A*A*(61 - 58*T + T*T + 600*C - 330*e0sq)/720))));

                if (y < 0) {
                    y = 10000000 + y;
                }

                var obj = {
                    coordenadaE: x,
                    coordenadaN: y,
                    fusoUTM: {
                        zona: utmz,
                        hemisferio: phi < 0 ? "S" : "N",
                        name: phi < 0 ? "SUL_"+ utmz : "NORTE_"+ utmz
                    }
                  };
                return obj;
            }

            function utmToGd(m) {
                var easting = m.coordenadaE;
                var northing = m.coordenadaN;
                var zone = m.fusoUTM.zona;
                var northernHemisphere = m.fusoUTM.hemisferio === "N";

                if (!northernHemisphere){
                    northing = 10000000 - northing;
                }

                var a = 6378137;
                var e = 0.081819191;
                var e1sq = 0.006739497;
                var k0 = 0.9996;

                var arc = northing / k0;
                var mu = arc / (a * (1 - Math.pow(e, 2) / 4.0 - 3 * Math.pow(e, 4) / 64.0 - 5 * Math.pow(e, 6) / 256.0));

                var ei = (1 - Math.pow((1 - e * e), (1 / 2.0))) / (1 + Math.pow((1 - e * e), (1 / 2.0)));

                var ca = 3 * ei / 2 - 27 * Math.pow(ei, 3) / 32.0;

                var cb = 21 * Math.pow(ei, 2) / 16 - 55 * Math.pow(ei, 4) / 32;
                var cc = 151 * Math.pow(ei, 3) / 96;
                var cd = 1097 * Math.pow(ei, 4) / 512;
                var phi1 = mu + ca * Math.sin(2 * mu) + cb * Math.sin(4 * mu) + cc * Math.sin(6 * mu) + cd * Math.sin(8 * mu);

                var n0 = a / Math.pow((1 - Math.pow((e * Math.sin(phi1)), 2)), (1 / 2.0));

                var r0 = a * (1 - e * e) / Math.pow((1 - Math.pow((e * Math.sin(phi1)), 2)), (3 / 2.0));
                var fact1 = n0 * Math.tan(phi1) / r0;

                var _a1 = 500000 - easting;
                var dd0 = _a1 / (n0 * k0);
                var fact2 = dd0 * dd0 / 2;

                var t0 = Math.pow(Math.tan(phi1), 2);
                var Q0 = e1sq * Math.pow(Math.cos(phi1), 2);
                var fact3 = (5 + 3 * t0 + 10 * Q0 - 4 * Q0 * Q0 - 9 * e1sq) * Math.pow(dd0, 4) / 24;

                var fact4 = (61 + 90 * t0 + 298 * Q0 + 45 * t0 * t0 - 252 * e1sq - 3 * Q0 * Q0) * Math.pow(dd0, 6) / 720;

                var lof1 = _a1 / (n0 * k0);
                var lof2 = (1 + 2 * t0 + Q0) * Math.pow(dd0, 3) / 6.0;
                var lof3 = (5 - 2 * Q0 + 28 * t0 - 3 * Math.pow(Q0, 2) + 8 * e1sq + 24 * Math.pow(t0, 2)) * Math.pow(dd0, 5) / 120;
                var _a2 = (lof1 - lof2 + lof3) / Math.cos(phi1);
                var _a3 = _a2 * 180 / Math.PI;

                var latitude = 180 * (phi1 - fact1 * (fact2 + fact3 + fact4)) / Math.PI;

                if (!northernHemisphere){
                    latitude = -latitude;
                }

                var longitude = ((zone > 0) && (6 * zone - 183.0) || 3.0) - _a3;

                return {
                    latitude: {
                        hemisferio: latitude < 0 ? "S" : "N",
                        decimal: latitude
                    },
                    longitude: {
                        hemisferio: longitude < 0 ? "O" : "L",
                        decimal: longitude
                    }
                };
            }

            function gmsToGd(gms) {
                var latitude = parseFloat(gms.latitude.graus) + parseFloat(gms.latitude.minutos/60 + gms.latitude.segundos/3600);
                var longitude = parseFloat(gms.longitude.graus) + parseFloat(gms.longitude.minutos/60 + gms.longitude.segundos/3600);

                if (gms.latitude.hemisferio === "S") {
                    latitude *= -1;
                }

                if (gms.longitude.hemisferio === "O") {
                    longitude *= -1;
                }

                return {
                    latitude: {
                        hemisferio: gms.latitude.hemisferio,
                        decimal: latitude
                    },
                    longitude: {
                        hemisferio: gms.longitude.hemisferio,
                        decimal: longitude
                    }
                };
            }

            function gdToGms(gd) {
                var obj = {
                    latitude: {
                        hemisferio: null,
                        graus: null,
                        minutos: null,
                        segundos: null
                    },
                    longitude: {
                        hemisferio: null,
                        graus: null,
                        minutos: null,
                        segundos: null
                    }
                };

                var latitude = Math.abs(gd.latitude.decimal);
                var longitude = Math.abs(gd.longitude.decimal);

                obj.latitude.graus = parseInt(latitude);
                obj.latitude.minutos = parseInt((latitude - obj.latitude.graus) * 60);
                obj.latitude.segundos = (latitude - obj.latitude.graus - (obj.latitude.minutos / 60)) * 3600;
                obj.latitude.hemisferio = gd.latitude.hemisferio;

                obj.longitude.graus = parseInt(longitude);
                obj.longitude.minutos = parseInt((longitude - obj.longitude.graus) * 60);
                obj.longitude.segundos = (longitude - obj.longitude.graus - (obj.longitude.minutos / 60)) * 3600;
                obj.longitude.hemisferio = gd.longitude.hemisferio;

                return obj;
            }

            function buscarGeometria(endereco) {
                return UfsService.RecuperaUF.query({sigla: endereco.uf}).$promise.then(
                    function (result) {
                        var enderecamentoImovel = {
                            uf: result[0].descricao,
                            municipio: endereco.municipio,
                            bairro: endereco.bairro
                        };

                        return $http.post(urlBase + "cartografia/geometria/por-endereco", enderecamentoImovel);
                    }
                );
            }

            return {
                transformarGeometria: transformarGeometria,
                buscarGeometria: buscarGeometria,
                utmToGd: utmToGd,
                gdToUtm: gdToUtm,
                gmsToGd: gmsToGd,
                gdToGms: gdToGms
            };
        }])
        .factory('SistemaReferenciaService', ["$http", "urlBase", function ($http, urlBase) {
            function buscarTodos() {
                return $http.get(urlBase + 'dominio/sistemas-referencia');
            }

            return {
                buscarTodos: buscarTodos
            };
        }])
        .factory('FusoUTMService', ["$http", "urlBase", function ($http, urlBase) {
            function buscarTodos() {
                return $http.get(urlBase + 'dominio/fusos-utm');
            }

            return {
                buscarTodos: buscarTodos
            };
        }]);
})();

(function(){
    'use strict';
    angular.module('su-cadastro-imovel').factory('UnidadeAutonomaService', ["$resource", "urlBase", function ($resource, urlBase) {

    function UnidadeAutonoma() {
        return {
            area: null,
            fracaoIdealCartorial: null,
            fracaoIdealCadastral: null,
            memorialDescritivo: null,
            numeroDormitorios: null,
            possuiGaragem: null,
            numeroVagasCobertas: null,
            numeroVagasDescobertas: null,
            observacoes: null,
            tipoGaragem: null,
            condicoesAcessibilidade: {
                semInformacoesAcessibilidade: null,
                vaga: null,
                mobilidade: null,
                banheiro: null,
                elevador: null,
                vagasDefFisicos: null,
                vagasIdosos: null,
                calcadasRebaixa: null,
                calcadasLivres: null,
                entradaLivre: null,
                pavimentosLivreCadRodas: null,
                balcaoInformacoes: null,
                pisoTatil: null,
                corredorLargo: null,
                sanitarioPublico: null,
                braile: null,
                libras: null,
                possuiLaudo: null,
                linkLaudo: null,
                possuiPlanoTrabalho: null,
                linkPlanoTrabalho: null
            },
            edificacao: {
                possuiElevador: null,
                nome: null,
                observacoes: null,
                arquivoGeometria: {},
                tipoEdificacao: {},
                tipoProjeto: null,
                documentacao: [ {nome: 'Plano de Proteção Contra Incêndio', possui: 'SEM_INFORMACAO', isPrecadastrado: true},
                                {nome: 'Habite-se', possui: 'SEM_INFORMACAO', isPrecadastrado: true},
                                {nome: 'Alvará de Construção/Reforma', possui: 'SEM_INFORMACAO', isPrecadastrado: true},
                                {nome: 'Laudo de Inspeção Predial', possui: 'SEM_INFORMACAO', isPrecadastrado: true},
                                {nome: 'Relatório de Acessibilidade', possui: 'SEM_INFORMACAO', isPrecadastrado: true}],
                dadosMedidor: []
            },
            fotos: [],
            plantas: []
        };
    }

        function arquivo() {
            function formDataObject (data) {
                var fd = new FormData();
                angular.forEach(data, function(value, key) {
                    fd.append(key, value);
                });
                return fd;
            }

            return $resource(urlBase + '/imovel/terreno/upload', null, {
                'save': {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {'Content-Type':undefined, enctype:'multipart/form-data'}
                }
            });
        }

        function novaUnidadeAutonoma() {
            return new UnidadeAutonoma();
        }

        return {
            novaUnidadeAutonoma: novaUnidadeAutonoma,
            arquivo: arquivo
        }
    }]);
})();

(function() {
    'use strict';
    angular.module('su-cadastro-imovel').factory('TerrenoService', ["$http", "urlBase", function($http, urlBase) {

        function formDataObject(data) {
            var fd = new FormData();
            angular.forEach(data, function(value, key) {
                fd.append(key, value);
            });
            return fd;
        }

        function uploadArquivo(arquivo) {

            return $http.post(urlBase + "imovel/terreno/upload", formDataObject(arquivo), {
                transformRequest: angular.identity,
                headers: {
                    'Content-Type': undefined
                }
            });
        }

        function validarFormularioTerreno(isFormValid, possuiGeometria, isGeometriaValida, isPontoNaInsercao, isCavidadeNaturalSubterranea) {
            var response = {
                isValid: true,
                message: ""
            };

            if (isFormValid) {
                if (!isCavidadeNaturalSubterranea) {
                    if (!possuiGeometria) {
                        response.isValid = false;
                        response.message = "É necessário informar a geometria do terreno";
                    } else if (!isGeometriaValida) {
                        response.isValid = false;
                        response.message = "Geometria inválida";
                     }  else if (!isPontoNaInsercao) {
                         response.isValid = false;
                         response.message = "O ponto do imóvel não se encontra sobre uma das linhas do vértice do polígono do terreno.";
                     }
                }
            } else {
                var erros = angular.element('.ng-invalid');
                for (var erro = 0, len = erros.length; erro < len; erro++) {
                    if (erros[erro].tagName !== 'FORM' && erros[erro].tagName !== 'NG-FORM') {
                        var escopoElemento = angular.element(erros[erro]).scope();
                        if (escopoElemento && escopoElemento.$pane) {
                            escopoElemento.$pane.expand();
                        }
                    }
                }

                response.isValid = false;
                response.message = "Necessário preencher os campos obrigatórios";
            }

            return response;
        }

        return {
            uploadArquivo: uploadArquivo,
            validarFormularioTerreno: validarFormularioTerreno
        };

    }]).factory("ValidaPontoTerrenoService", ["$resource", "urlBase", function($resource, urlBase) {

        return $resource(urlBase + 'imovel/validaGeometriaTerreno', {
            validar: {
                method: "POST",
                params: {
                    parametros: "@parametros",
                }
            },
            uuidTerreno: "@uuidTerreno"
        });
    }]).factory("ValidaAreaTerrenoService", ["$resource", "urlBase", function($resource, urlBase) {

        return $resource(urlBase + "imovel/fracionamento/area/validar/:ripOrigem/:area/:rip", {
            ripOrigem: "@ripOrigem",
            area: "@area",
            rip: "@rip"
        });
    }]).factory("ArquivosComplementaresUpload", ["$resource", "urlBase", function($resource, urlBase) {

        function formDataObject(data) {
            var fd = new FormData();
            angular.forEach(data, function(value, key) {
                fd.append(key, value);
            });
            return fd;
        }
        return $resource(urlBase + 'imovel/arquivos-complementares/upload', null, {
            'save': {
                method: 'POST',
                transformRequest: formDataObject,
                headers: { 'Content-Type': undefined, enctype: 'multipart/form-data' }
            }
        })
    }]).factory("DeletarArquivoComplementar", ["$resource", "urlBase", function($resource, urlBase) {

        return $resource(urlBase + "imovel/deletar-arquivo-complementar/:idArquivo", {
            idArquivo: "@idArquivo"
        });
    }]).factory("ValidaFracaoIdealTerrenoService", ["$resource", "urlBase", function($resource, urlBase) {

        return $resource(urlBase + "imovel/unidade-autonoma/fracao-ideal/validar/:idTerreno/:ripImovel/:area", {
            idTerreno: "@idTerreno",
            ripImovel: "@ripImovel",
            area: "@area"
        });
    }]);
})();

(function () {
  'use strict';
  angular.module('su-cadastro-imovel').controller('TerrenoController',
        ["$scope", "$timeout", "$window", "$mdDialog", "TerrenoService", "TipoEquipamento", "FormaLevantamento", "Dominio", "TipoTerreno", "NotificadorService", "$stateParams", "NOTIFICADORES", "TIPODESENHO", "MAPA_CONSTANTS_INCORPORACAO", "ValidaAreaTerrenoService", "ArquivosComplementaresUpload", "DeletarArquivoComplementar", "MensagensService", "SincronizarService", function ( $scope, $timeout, $window, $mdDialog, TerrenoService, TipoEquipamento, FormaLevantamento, Dominio, TipoTerreno, NotificadorService, $stateParams, NOTIFICADORES, TIPODESENHO, MAPA_CONSTANTS_INCORPORACAO, ValidaAreaTerrenoService, ArquivosComplementaresUpload, DeletarArquivoComplementar, MensagensService, SincronizarService) {

          var vetorizacaoEmTela = 333;
          var arquivoShapefile  = 222;
          var TIPO_DESENHO_VETORIAZACAO = TIPODESENHO.VETORIZACAOEMTELA;

          var SR_IMG_SENSORES_ORB = 1;
          var SR_AEROFOTOGAMETRIA = 2;
          var LEVANTAMENTO_TOPOGRAFICO = 3;
          var GPS_NAVEGACAO = 2;
          var LIMITE_RESOLUCAO_MEMORIAL = 0.3;
          var isAlterar = $stateParams.id ? true : false;
          var metro_quadrado = 'm2';

          var POR_FRACIONAMENTO = 3;
          var POR_FUSAO = 4;

          var notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES,
            featureIds = MAPA_CONSTANTS_INCORPORACAO.FEATURE_ID;

          var self = this;
          self.tiposDesenhoGeometriaTerreno = {
            tipoGeometriaCadastral: undefined,
            tipoGeometriaCartorial: undefined
          };

          self.desabilitarPreenchimentoMemorialDescritivo = false;
          self.arquivo = {};
            //DEFAULT TIPO TERRENO
          self.arquivoCartorial = {};
          self.habilitarUploadArquivoTerrenoCartorial = false;

          var conversor = 10000;
            //UNIDADE DE MEDIDA DEFAULT = METRO QUADRADO
          if ($scope.imovel.terreno.unidadeMedidaCadastral == undefined ||
            $scope.imovel.terreno.unidadeMedidaCadastral == null){
            $scope.imovel.terreno.unidadeMedidaCadastral = metro_quadrado;
          }
          if ($scope.imovel.terreno.unidadeMedidaCartorial == undefined ||
            $scope.imovel.terreno.unidadeMedidaCartorial == null){
            $scope.imovel.terreno.unidadeMedidaCartorial = metro_quadrado;
          }

            // if ($scope.imovel.terreno.unidadeMedidaCadastral == 'ha'){
            //     $scope.imovel.terreno.areaTotalCadastral = $scope.imovel.terreno.areaTotalCadastral/conversor;
            //     $scope.imovel.terreno.areaUniaoCadastral = $scope.imovel.terreno.areaUniaoCadastral/conversor;
            //     if ($scope.imovel.unidadeAutonoma.fracaoIdealCadastral  != undefined && $scope.imovel.unidadeAutonoma.fracaoIdealCadastral  != null){
            //         $scope.imovel.unidadeAutonoma.fracaoIdealCadastral  = $scope.imovel.unidadeAutonoma.fracaoIdealCadastral /conversor;
            //     }
            // }

          NotificadorService.subscribe($scope, function(evento, escopo, arquivo){
            self.arquivoCartorial = {};
            $scope.$emit('limparDescricaoCartorial', self.arquivoCartorial);
            $('#desenhar-terreno-cadastral').removeClass('disabled');
            $('#desenhar-terreno-cadastral').css('display','block');
            $('#desenhar-terreno').removeClass('disabled');
            $('#desenhar-terreno').css('display','block');
          }, notifiers.DELETESHAPEFILECARTORIAL);

          NotificadorService.subscribe($scope, function(evento, escopo, terreno){
            self.arquivo = terreno.arquivoCadastral || {};
            self.arquivoCartorial = terreno.arquivoCartorial;
          }, NOTIFICADORES.TERRENOEDICAO);

          self.habilitarUploadArquivoTerrenoCadastral = false;

          //  $scope.isMapEmpty = true;
          self.isMapEmptyCartorial = true;


          $scope.$watch('terrenoCtrl.arquivo', function(newValue, oldValue) {
            self.desabilitarPreenchimentoMemorialDescritivo = self.isPreenchimentoMemorialSR() || self.isPreenchimentoMemorialGPS();
          });

          TipoEquipamento.query(function(response){
            self.tipoEquipamento = response;
          });

          FormaLevantamento.query(function(response) {
            self.formaLevantamento = response;
          });


          self.getAlterarParaShapeFileConfirmDialog = function(ev){
            return $mdDialog.confirm({focusOnOpen : false})
                        .title('Alterar para arquivo (shapefile)')
                        .textContent('Já existe uma geometria vetorizada em tela para o terreno. ' +
                            'Deseja realmente excluir a geometria para o envio de um arquivo Shapefile?')
                        .targetEvent(ev)
                        .ok('Sim')
                        .cancel('Não');
          };

          self.alterarParaArquivoShapeFile = function (ev) {
            var geometriaTerreno = $scope.imovel.terreno.geometriaTerreno,
              terreno = $scope.imovel.terreno;
            if (geometriaTerreno && !terreno.geometriaTerrenoCartorial){
              if (terreno.tipoDesenho === 'vetorizacao') {
                var confirm = $mdDialog.confirm({
                  title: 'Alterar para arquivo',
                  focusOnOpen : false,
                  textContent : 'Já existe uma geometria vetorizada em tela para o terreno. ' +
                                'Deseja realmente excluir a geometria para o envio de um arquivo?',
                  ok : 'Sim',
                  cancel : 'Não'
                });

                $mdDialog.show(confirm).then(
                                function(){
                                  self.habilitarUploadArquivoTerrenoCadastral = true;
                                  $('#desenhar-terreno-cadastral').css('display','none');
                                  $('#desenhar-terreno').css('display','none');
                                  NotificadorService.notify($scope, 'terrenoCadastral', notifiers.LIMPAR_DESENHO);
                                },
                                function () {
                                  $scope.imovel.terreno.tipoDesenho = 'vetorizacao';
                                }
                            );
              }
            }

          };

          self.alterarParaArquivoShapeFileCartorial = function(ev){
            if(!self.isMapEmptyCartorial){
              var confirm = self.getAlterarParaShapeFileConfirmDialog();
              $mdDialog.show(confirm).then(
                        function(){
                          self.isMapEmptyCartorial = true;
                          self.habilitarUploadArquivoTerrenoCartorial = true;
                          NotificadorService.notify($scope, null, NOTIFICADORES.LIMPARDESENHOCARTORIAL);
                          self.showUploadArquivoTerrenoDialogCartorial(ev);
                        },
                        function(){
                          self.terreno.tipoDesenhoCartorial = vetorizacaoEmTela;
                        }
                    );
            }else{
              self.habilitarUploadArquivoTerrenoCartorial = true;
            }
          };

          self.alterarParaDescricaoNaoGeorreferenciada = function(ev){
            if(self.arquivoCartorial && self.arquivoCartorial.arquivo){
              var confirm = $mdDialog.confirm()
                    .title('Alterar para descrição não georreferenciada')
                    .textContent('Já existe uma geometria enviada por meio de um arquivo Shapefile. ' +
                        'Deseja realmente excluir a geometria existente?')
                    .targetEvent(ev)
                    .ok('Sim')
                    .cancel('Não');
              $mdDialog.show(confirm).then(
                        function(){
                          NotificadorService.notify($scope, 'terrenoCartorial', notifiers.LIMPAR_DESENHO);
                          NotificadorService.notify($scope, null, notifiers.DELETESHAPEFILECARTORIAL);
                          self.arquivoCartorial = {};
                        },
                        function(){
                          self.terreno.tipoDesenhoCartorial = vetorizacaoEmTela;
                        }
                    );
            }else{
              self.habilitarUploadArquivoTerrenoCartorial = false;
            }
          };

          self.alterarParaVetorizacaoEmTela = function (ev) {
            if(self.arquivoCartorial && self.arquivoCartorial.arquivo){

            } else {
              if(self.arquivo && self.arquivo.arquivo) {

                var confirm = $mdDialog.confirm({
                  title: 'Alterar para vetorização em tela',
                  focusOnOpen : false,
                  textContent : 'Já existe uma geometria enviada por meio de um arquivo Shapefile. ' +
                            'Deseja realmente excluir a geometria existente por uma vetorização em tela?',
                  ok : 'Sim',
                  cancel : 'Não'
                });

                $mdDialog.show(confirm).then(
                            function(){
                              NotificadorService.notify($scope, null, NOTIFICADORES.LIMPARDESENHOCADASTRAL);
                              $scope.isMapEmpty = true;
                              NotificadorService.notify($scope, 'terrenoCadastral', notifiers.LIMPAR_DESENHO);
                              NotificadorService.notify($scope, null, NOTIFICADORES.DELETESHAPEFILECADASTRAL);
                              self.arquivo = {};
                              self.habilitarUploadArquivoTerrenoCadastral = false;
                              $('#desenhar-terreno-cadastral').removeClass('disabled');
                              $('#desenhar-terreno-cadastral').css('display','block');
                              $('#desenhar-terreno').removeClass('disabled');
                              $('#desenhar-terreno').css('display','block');
                              $scope.imovel.terreno.memorialDescritivoCadastral = '';
                            },
                            function () {
                              $scope.imovel.terreno.tipoDesenho = 'shapeFile';
                              $('#desenhar-terreno-cadastral').css('display','none');
                              $('#desenhar-terreno').css('display','none');
                            }
                        );
              }else{
                self.habilitarUploadArquivoTerrenoCadastral = false;
                $('#desenhar-terreno-cadastral').css('display','block');
                $('#desenhar-terreno-cadastral').removeClass('disabled');
                $('#desenhar-terreno').removeClass('disabled');
                $('#desenhar-terreno').css('display','block');
              }
            }
          };

          self.validaAreaUniaoCadastral = function(){
            if ($scope.imovel.terreno.areaUniaoCadastral >= $scope.imovel.terreno.areaTotalCadastral){
              $scope.forms.terrenoForm.areaUniaoCadastral.$setValidity('areaUniaoCadastral', false);
              MensagensService.exibirMensagemErro('A Área da União não pode ser maior que a Área Total.');
            } else {
              $scope.forms.terrenoForm.areaUniaoCadastral.$setValidity('areaUniaoCadastral', true);
            }
          };

          self.validaAreaUniaoCartorial = function(){
            if ($scope.imovel.terreno.areaUniaoCartorial >= $scope.imovel.terreno.areaTotalCartorial){
              $scope.forms.terrenoForm.areaUniaoCartorial.$setValidity('areaUniaoCartorial', false);
              MensagensService.exibirMensagemErro('A Área da União não pode ser maior que a Área Total.');
            }
          };
          self.calcularFracaoIdealUniaoCadastral = function(){
            self.validaAreaUniaoCadastral();
            if ($scope.imovel.terreno.geometriaTerreno &&
                    $scope.imovel.tipoImovel.tipoPai === 'UNIDADE AUTÔNOMA' &&
                    $scope.imovel.proprietario.isIntegralmente){
              $scope.medidaConvertida = $scope.imovel.terreno.areaTotalCadastral;
              if ($scope.forms.terrenoForm.areaUniaoCadastral) {
                var fracaoIdealImovelCadastral = $scope.imovel.unidadeAutonoma.fracaoIdealCadastral ;
                var areaUniaoCadastral = $scope.imovel.terreno.areaUniaoCadastral;
                var areaTotalCadastral = $scope.imovel.terreno.areaTotalCadastral;
                if(fracaoIdealImovelCadastral != undefined && arFeaUniaoCadastral != undefined && areaTotalCadastral != undefined){
                  $scope.imovel.terreno.fracaoIdealUniaoCadastral =
                            fracaoIdealImovelCadastral * (areaUniaoCadastral / areaTotalCadastral);


                  if ($scope.imovel.terreno.unidadeMedidaCadastral == 'ha'){
                      $scope.imovel.terreno.fracaoIdealUniaoCadastral = $scope.imovel.terreno.fracaoIdealUniaoCadastral*10000;

                    }
                }
              }
            }
          };
          self.validaFracaoIdealImovelCadastral = function (){
            if ($scope.imovel.terreno.geometriaTerreno && $scope.imovel.tipoImovel.tipoPai === 'UNIDADE AUTÔNOMA'){
              if($scope.imovel.unidadeAutonoma.fracaoIdealCadastral  === 0){

                MensagensService.exibirMensagemErro('A Fração Ideal do Imóvel deve ser diferente de 0.');
                angular.element(document.querySelector('#fracaoIdealImovelCadastral')).focus();
                $scope.forms.terrenoForm.fracaoIdealImovelCadastral.$setValidity('fracaoIdealImovelCadastral', false);

              }else if($scope.imovel.proprietario.isIntegralmente && $scope.imovel.terreno.areaUniaoCadastral != undefined &&
                        $scope.imovel.unidadeAutonoma.fracaoIdealCadastral  != undefined &&
                        $scope.imovel.unidadeAutonoma.fracaoIdealCadastral  >= $scope.imovel.terreno.areaUniaoCadastral){

                MensagensService.exibirMensagemErro('A Fração Ideal do Imóvel deve ser inferior à Área da União');
                $scope.forms.terrenoForm.fracaoIdealImovelCadastral.$setValidity('fracaoIdealImovelCadastral', false);

              }else if($scope.imovel.terreno.areaTotalCadastral != undefined && $scope.imovel.unidadeAutonoma.fracaoIdealCadastral  != undefined &&
                        $scope.imovel.unidadeAutonoma.fracaoIdealCadastral  > $scope.imovel.terreno.areaTotalCadastral){

                  MensagensService.exibirMensagemErro('A Fração Ideal do Imóvel deve ser igual ou inferior à Área Total');
                  $scope.forms.terrenoForm.fracaoIdealImovelCadastral.$setValidity('fracaoIdealImovelCadastral', false);

                }else{
                  $scope.forms.terrenoForm.fracaoIdealImovelCadastral.$setValidity('fracaoIdealImovelCadastral', true);

                }
            }
          };
          self.calcularFracaoIdealUniaoCartorial = function(){
            if ($scope.imovel.terreno.geometriaTerreno && $scope.imovel.tipoImovel.tipoPai === 'UNIDADE AUTÔNOMA'){
              if($scope.imovel.unidadeAutonoma.fracaoIdealCartorial === 0){
                MensagensService.exibirMensagemErro('A Fração Ideal do Imóvel deve ser diferente de 0');
                angular.element(document.querySelector('#fracaoIdealImovelCartorial')).focus();
                $scope.forms.terrenoForm.fracaoIdealImovelCartorial.$setValidity('fracaoIdealImovelCartorial', false);

              }else if($scope.imovel.proprietario.isIntegralmente && $scope.imovel.terreno.areaUniaoCartorial != undefined &&
                        $scope.imovel.unidadeAutonoma.fracaoIdealCartorial != undefined &&
                        $scope.imovel.unidadeAutonoma.fracaoIdealCartorial >= $scope.imovel.terreno.areaUniaoCartorial){

                MensagensService.exibirMensagemErro('A Fração Ideal do Imóvel deve ser inferior à Área da União');
                $scope.forms.terrenoForm.fracaoIdealImovelCartorial.$setValidity('fracaoIdealImovelCartorial', false);

              }else if($scope.imovel.terreno.areaTotalCartorial != undefined && $scope.imovel.unidadeAutonoma.fracaoIdealCartorial != undefined &&
                        $scope.imovel.unidadeAutonoma.fracaoIdealCartorial > $scope.imovel.terreno.areaTotalCartorial){

                  MensagensService.exibirMensagemErro('A Fração Ideal do Imóvel deve ser igual ou inferior à Área Total');
                  $scope.forms.terrenoForm.fracaoIdealImovelCartorial.$setValidity('fracaoIdealImovelCartorial', false);

                }else{

                  $scope.forms.terrenoForm.fracaoIdealImovelCartorial.$setValidity('fracaoIdealImovelCartorial', true);

                }
            }

          };

          var salvarArquivo = function () {

            var arquivo = {
              zipfile: self.arquivo.zipfile,
              escala : self.arquivo.escala,
              tipoFormaLevantamento: self.arquivo.tipoFormaLevantamento.id,
              dataPlanta: self.arquivo.dataPlanta
            };

            if(self.arquivo.resolucaoEspacial != undefined) {
              arquivo.resolucaoEspacial = self.arquivo.resolucaoEspacial;
            }

            if (self.arquivo.tipoEquipamento != undefined) {
              arquivo.tipoEquipamento = self.arquivo.tipoEquipamento.id;
            }

            JSON.stringify(arquivo);

            TerrenoService.uploadArquivo(arquivo,
                    function (response) {
                      if(isAlterar){
                        self.notificarArquivoShapefileSalvoEdicao(response);
                      }else{
                        $scope.$emit('uploadShapefile', response.geometry);
                        NotificadorService.notify($scope, {feature: response.geometry, tipoTerreno: featureIds.TERRENO_CADASTRAL}, notifiers.UPLOAD_SHAPEFILE);
                        $scope.$emit('uploadShapefileDescricaoCadastral', response.memorialDescritivoCadastral);
                      }

                      self.arquivo = response || {};
                      self.terreno.memorialDescritivoCadastral = self.arquivo.memorialDescritivoCadastral;
                      delete self.arquivo.memorialDescritivoCadastral;
                      if ($scope.imovel.incorporacao.tipoIncorporacao.id != POR_FUSAO) {
                        $scope.imovel.terreno.areaTotalCadastral = self.arquivo.areaGeometria;
                      }

                      delete self.arquivo.areaGeometria;
                      NotificadorService.notify($scope, self.arquivo, NOTIFICADORES.SHAPEFILECADASTRAL);
                    },
                    function (error) {
                      MensagensService.exibirMensagemErro(error.data.message);
                    });
          };

          self.isPreenchimentoMemorialSR = function () {
            var tipoFormaLevantamento = self.arquivo.tipoFormaLevantamento;

            return self.arquivo.resolucaoEspacial <= LIMITE_RESOLUCAO_MEMORIAL &&
                    (tipoFormaLevantamento === SR_IMG_SENSORES_ORB || tipoFormaLevantamento === SR_AEROFOTOGAMETRIA);
          };

          self.isPreenchimentoMemorialGPS = function() {
            return angular.isDefined(self.arquivo) && angular.isDefined(self.arquivo.tipoEquipamento)
                    && (self.arquivo.tipoFormaLevantamento.id === LEVANTAMENTO_TOPOGRAFICO
                    && self.arquivo.tipoEquipamento.id ==! GPS_NAVEGACAO);
          };

          self.excluirArquivoTerrenoCadastral = function (ev) {
            if(!$scope.arquivo) {
              var confirm = $mdDialog.confirm({focusOnOpen : false})
                        .title('Remover arquivo (shapefile)')
                        .textContent('Se houver no mapa de localização do terreno alguma geometria enviada por meio de um arquivo shapefile, essa será excluída.' +
                            ' Deseja realmente excluir o arquivo Shapefile?')
                        .targetEvent(ev)
                        .ok('Sim')
                        .cancel('Não');
              $mdDialog.show(confirm).then(
                        function() {
                          NotificadorService.notify($scope, null, NOTIFICADORES.DELETESHAPEFILECADASTRAL);
                          self.habilitarUploadArquivoTerrenoCadastral = true;
                          TipoTerreno.setTipoTerreno(Dominio.Constantes.tipoTerrenoCadastral);
                          NotificadorService.notify($scope, 'terrenoCadastral', notifiers.LIMPAR_DESENHO);
                          $scope.imovel.terreno.unidadeMedidaCadastral = undefined;
                          $scope.imovel.terreno.memorialDescritivoCadastral = undefined;
                          if ($scope.imovel.incorporacao.tipoIncorporacao.id != POR_FUSAO) {
                            $scope.imovel.terreno.areaTotalCadastral = undefined;
                          }
                          self.arquivo = {};
                        }
                    );
            }
          };

          self.excluirArquivoTerrenoCartorial = function (ev) {
            if(!$scope.arquivoCartorial) {
              var confirm = $mdDialog.confirm({focusOnOpen : false})
                        .title('Remover arquivo (shapefile)')
                        .textContent('Se houver no mapa de localização do terreno alguma geometria enviada por meio de um arquivo shapefile, essa será excluída.' +
                            ' Deseja realmente excluir o arquivo Shapefile?')
                        .targetEvent(ev)
                        .ok('Sim')
                        .cancel('Não');
              $mdDialog.show(confirm).then(
                        function() {
                          NotificadorService.notify($scope, null, notifiers.DELETESHAPEFILECARTORIAL);
                          NotificadorService.notify($scope);
                          self.habilitarUploadArquivoTerrenoCartorial = true;
                          self.habilitarUploadArquivoTerrenoCadastral = true;
                          TipoTerreno.setTipoTerreno(Dominio.Constantes.tipoTerrenoCartorial);
                          NotificadorService.notify($scope, 'terrenoCartorial', notifiers.LIMPAR_DESENHO);
                          self.arquivoCartorial = {};
                          $scope.$emit('limparDescricaoCartorial', self.arquivoCartorial);
                          $('#desenhar-terreno-cadastral').removeClass('disabled');
                          $('#desenhar-terreno-cadastral').css('display','block');
                          $('#desenhar-terreno').removeClass('disabled');
                          $('#desenhar-terreno').css('display','block');
                        }
                    );
            }
          };

          var uploadArquivoTerrenoDialogController = function($scope, $mdDialog, arquivo,
                tipoEquipamento, formaLevantamento) {
            this.arquivo = arquivo;
            this.tipoEquipamento = tipoEquipamento;
            this.formaLevantamento = formaLevantamento;
            this.hoje = new Date();
            this.fechar = function () {
              $mdDialog.cancel();
            };

            this.confirmar = function () {
              $scope.submittedArquivoCadastral = true;
              if (self.arquivo && self.arquivo.tipoFormaLevantamento != undefined) {
                $scope.arquivoLevantamento = self.arquivo.tipoFormaLevantamento;
              } else if (self.arquivo && self.arquivo.resolucaoEspacial != undefined) {
                $scope.arquivoLevantamento = self.arquivo.resolucaoEspacial;
              }
              if ($scope.forms.uploadArquivoShapefile.$error.required) {
                MensagensService.exibirMensagemCamposObrigatorios();
              } else {
                $mdDialog.hide();
              }
            };
          };
          uploadArquivoTerrenoDialogController.$inject = ["$scope", "$mdDialog", "arquivo", "tipoEquipamento", "formaLevantamento"];
          self.showShapeFileCadastralJaEnviado = function(ev){
            $mdDialog.show(self.getShapefileJaEnviadoDialog()).then(function(){
              self.showUploadArquivoTerrenoDialog(ev);
            }, function(){
            });
          };

          self.showShapeFileCartorialJaEnviado = function(ev){
            $mdDialog.show(self.getShapefileJaEnviadoDialog()).then(function(){
              self.showUploadArquivoTerrenoDialogCartorial(ev);
            }, function(){
            });
          };

          self.getShapefileJaEnviadoDialog = function(){
            return $mdDialog.confirm({
              focusOnOpen : false,
              textContent : 'Para enviar um novo arquivo shapefile será necessário excluir o arquivo existente. Deseja realmente excluir?',
              ok : 'Sim',
              cancel : 'Não'
            });
          };


            // Dialog para upload do arquivo do terreno
          self.showUploadArquivoTerrenoDialog = function(ev) {
            if(!self.arquivo){
              self.arquivo = {};
            }
            if(self.arquivo.dataPlanta != undefined){
              self.arquivo.dataPlanta = new Date(self.arquivo.dataPlanta);
            }

            TipoTerreno.setTipoTerreno(Dominio.Constantes.tipoTerrenoCadastral);
            $mdDialog.show({
              controller: uploadArquivoTerrenoDialogController,
              controllerAs: 'uploadArqTerrenoCtrl',
              templateUrl: 'scripts-cadastro-imovel/pages/terreno/view/dialog-upload-arquivo.html',
              parent: angular.element(document.body),
              targetEvent: ev,
              clickOutsideToClose:true,
              locals: {
                arquivo: self.arquivo,
                formaLevantamento: self.formaLevantamento,
                tipoEquipamento: self.tipoEquipamento
              }
            }).then(function() {
              salvarArquivo();
            });
          };

            // Dialog para upload do arquivo do terreno cartorial
          self.showUploadArquivoTerrenoDialogCartorial = function(ev) {
            TipoTerreno.setTipoTerreno(Dominio.Constantes.tipoTerrenoCartorial);
            $mdDialog.show({
              controller: uploadArquivoTerrenoDialogControllerCartorial,
              templateUrl: 'scripts-cadastro-imovel/pages/terreno/view/dialog-upload-arquivo-cartorial.html',
              parent: angular.element(document.body),
              targetEvent: ev,
              clickOutsideToClose:true,
              locals: {
                arquivo: self.arquivoCartorial,
                formaLevantamento: self.formaLevantamento,
                tipoEquipamento: self.tipoEquipamento
              }
            }).then(function() {
              salvarArquivoCartorial();
            });

          };

          var uploadArquivoTerrenoDialogControllerCartorial = function($scope, $mdDialog, arquivo,
                    tipoEquipamento, formaLevantamento) {
            $scope.arquivoCartorial = arquivo;
            $scope.tipoEquipamento = tipoEquipamento;
            $scope.formaLevantamento = formaLevantamento;
            $scope.hoje = new Date();
            $scope.fechar = function () {
              $mdDialog.cancel();
            };


            $scope.confirmar = function () {
              $scope.submittedArquivoTerreno = true;
              if(self.arquivoCartorial && self.arquivoCartorial.tipoFormaLevantamento != undefined){
                $scope.arquivoLevantamento = self.arquivoCartorial.tipoFormaLevantamento;
              }else if(self.arquivoCartorial && self.arquivoCartorial.resolucaoEspacial != undefined) {
                $scope.arquivoLevantamento = self.arquivoCartorial.resolucaoEspacial;
              }
              if($scope.forms.uploadArquivoShapefile.$error.required){
                MensagensService.exibirMensagemCamposObrigatorios();
              }else{
                $mdDialog.hide();
              }
            };
          };
          uploadArquivoTerrenoDialogControllerCartorial.$inject = ["$scope", "$mdDialog", "arquivo", "tipoEquipamento", "formaLevantamento"];

          self.notificarArquivoShapefileSalvoEdicao = function(response){
            NotificadorService.notify($scope, response.geometry, NOTIFICADORES.UPLOADSHAPEFILESUCCESSEDICAO);
            if(TipoTerreno.getTipoTerreno() === Dominio.Constantes.tipoTerrenoCartorial){
              NotificadorService.notify($scope, response.memorialDescritivoCartorial, NOTIFICADORES.UPLOADSHAPEFILEDESCRICAOCARTORIALEDICAO);
              NotificadorService.notify($scope, null, NOTIFICADORES.DESABILITARDESENHOCADASTRALEDICAO);
            }else{
              NotificadorService.notify($scope, response.memorialDescritivoCartorial, NOTIFICADORES.UPLOADSHAPEFILEDESCRICAOCARTORIALEDICAO);
            }
          };

          var salvarArquivoCartorial = function () {
            var arquivo = {
              zipfile: self.arquivoCartorial.zipfile,
              escala : self.arquivoCartorial.escala,
              tipoFormaLevantamento: self.arquivoCartorial.tipoFormaLevantamento.id,
              dataPlanta: self.arquivoCartorial.dataPlanta
            };

            if(angular.isDefined(self.arquivoCartorial.resolucaoEspacial)){
              arquivo.resolucaoEspacial = self.arquivoCartorial.resolucaoEspacial;
            }else{
              arquivo.resolucaoEspacial=1;
            }

            if (angular.isDefined(self.arquivoCartorial.tipoEquipamento)) {
              arquivo.tipoEquipamento = self.arquivoCartorial.tipoEquipamento.id;
            }

            TerrenoService.uploadArquivo(arquivo).then(function(response){

              self.habilitarUploadArquivoTerrenoCadastral = true;
              self.arquivoCartorial = response;
              self.terreno.memorialDescritivoCartorial = self.arquivoCartorial.memorialDescritivoCartorial;
              if ($scope.imovel.incorporacao.tipoIncorporacao.id != POR_FUSAO) {
                $scope.imovel.terreno.areaTotalCartorial = self.arquivoCartorial.areaGeometria;
              }
              delete self.arquivoCartorial.areaGeometria;
              NotificadorService.notify($scope, self.arquivoCartorial, NOTIFICADORES.SHAPEFILECARTORIAL);
              $scope.imovel.terreno.tipoDesenho = 'shapeFile';
              self.terreno.tipoDesenho = 'shapeFile';

              if(isAlterar){
                self.notificarArquivoShapefileSalvoEdicao(response);
              }else{
                $scope.$emit('uploadShapefile', response.geometry);
                NotificadorService.notify($scope, {feature: response.geometry, tipoTerreno: featureIds.TERRENO_CARTORIAL}, notifiers.UPLOAD_SHAPEFILE);
                $scope.$emit('uploadShapefileDescricaoCartorial', response.memorialDescritivoCartorial);
                $scope.$emit('desabilitarDesenhoCadastral');
              }
            }, function(error){
              MensagensService.exibirMensagemErro(error.data.message);
            });
          };

          self.isEspelhoDagua = function () {
            return $scope.imovel && $scope.imovel.tipoImovel && $scope.imovel.tipoImovel.id === 3;
          };

          self.isLoteComEdificacao = function () {
            return $scope.imovel && $scope.imovel.tipoImovel && $scope.imovel.tipoImovel.id === 2;
          };

          var TIPO_IMOVEL_UNIDADE_AUTONOMA = 'UNIDADE AUTÔNOMA';
          self.isTipoImovelUnidadeAutonoma = function () {
            return $scope.imovel && $scope.imovel.tipoImovel && $scope.imovel.tipoImovel.tipoPai === TIPO_IMOVEL_UNIDADE_AUTONOMA;
          };

          self.verificarAreaTotalValidaCadastral = function () {
            if ($scope.imovel.incorporacao.tipoIncorporacao.id === POR_FRACIONAMENTO && $scope.imovel.terreno.areaTotalCadastral && !$scope.imovel.terreno.geometriaTerrenoCartorial) {

              ValidaAreaTerrenoService.get({
                ripOrigem: $scope.imovel.incorporacao.codigoOrigem,
                area: $scope.imovel.terreno.areaTotalCadastral,
                rip: $scope.imovel.rip
              }).$promise.then( function (response) {
                if (!response.isValid) {
                    $scope.imovel.terreno.areaTotalCadastral = undefined;
                    MensagensService.exibirMensagemErro('Área total inválida');
                  }
              });
            }
          };


          self.verificarAreaTotalValidaCartorial = function () {
            if ($scope.imovel.incorporacao.tipoIncorporacao.id === POR_FRACIONAMENTO && $scope.imovel.terreno.areaTotalCartorial) {

              ValidaAreaTerrenoService.get({
                ripOrigem: $scope.imovel.incorporacao.codigoOrigem,
                area: $scope.imovel.terreno.areaTotalCartorial,
                rip: $scope.imovel.rip
              }).$promise.then( function (response) {
                if (!response.isValid) {
                    $scope.imovel.terreno.areaTotalCartorial = undefined;
                    MensagensService.exibirMensagemErro('Área total inválida');
                  }
              });
            }
          };

          self.arquivosComplementares = {};
          self.arquivosComplementares.descricao = undefined;
          self.arquivosComplementares.data = undefined;
          self.arquivosComplementares.arquivo = undefined;
          self.arquivosComplementares.url = undefined;
          self.hoje = new Date();

          if($scope.imovel.id === undefined) {
            $scope.imovel.arquivosComplementaresLista = [];
          }
        }]);
})();

(function () {
    "use strict";

    angular.module('su-cadastro-imovel').factory('ImovelSuspensaoService', ["$resource", "$http", "urlBase", function ($resource, $http, urlBase) {

        var _resources={
            imovelSuspensao:$resource(urlBase + 'imovel/suspensao/cadastro', {},
                {
                    save:{method:"POST"}
                }),
            detalharSuspensao:$resource(urlBase + 'imovel/suspensao/detalhar/:id',{id: "@id"},
                {
                    get:{method:"GET"}
                }),
            cancelarSuspensao:$resource(urlBase + 'imovel/suspensao/cancelar', {
                    numeroProcesso : "@numeroProcesso", numeroProcessoCancelamento : "@numeroProcessoCancelamento", motivo : "@motivo",
                    numeroFolha : "@numeroFolha", dataVigencia : "@dataVigencia", observacao : "@observacao"
                },
                {
                    'save' : {
                        method : "POST",
                        taransformRequest : function(data){
                            return angular.toJson(data);
                        },
                        params : {
                            numeroProcesso : "@numeroProcesso", numeroProcessoCancelamento : "@numeroProcessoCancelamento", motivo : "@motivo",
                            numeroFolha : "@numeroFolha", dataVigencia : "@dataVigencia", observacao : "@observacao"
                        }
                    }
                }),
            buscarCancelados:$resource(urlBase + 'imovel/suspensao/recuperar/cancelar/:id',{id:'@id'},
                {
                    get:{method:"GET",transformResponse:function(data){
                        return {data:data};
                    }}
                })
        };


        var buscarMotivacoes = function () {
            return $resource(urlBase + "dominio/motivo-suspensao/all");
        };

        var buscarTiposSuspensao = function () {
            return $resource(urlBase + "dominio/tipo-suspensao-imovel/all");
        };

        var buscarRestricoes = function () {
            return $resource(urlBase + 'dominio/restricoes-suspensao/all');
        };

        var cancelarSuspensao = function (cancelamento) {
            return $http.post(urlBase + 'imovel/suspensao/cancelar', cancelamento);
        };

        return {
            save:function($object){
                return _resources.imovelSuspensao.save($object).$promise;
            },
            detalharSuspensao:function($resource){
                return _resources.detalharSuspensao.get($resource).$promise;
            },
            buscarCancelados:function($resource){
                return _resources.buscarCancelados.get($resource).$promise;
            },
            getMotivacoes: buscarMotivacoes,
            getTiposSuspensao: buscarTiposSuspensao,
            getRestricoes: buscarRestricoes,
            cancelarSuspensao: cancelarSuspensao
        };

    }]).factory("MotivosSuspensao", ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'dominio/motivo-cancelamento-suspensao/all');

    }]).factory('ValidarRips', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/suspensao/rips/validar',
                {
                    rips: "@rips",
                    restricoes: "@restricoes"
                },
                {
                    validar:{
                        method:"GET",
                        params: {
                            rips: "@rips",
                            restricoes: "@restricoes"
                        }
                    }
                }
            );
    }]);

})();

(function() {
  'use strict';
  angular
    .module('su-cadastro-imovel')
    .controller('selectRestricaoController', ["$scope", "$element", function($scope, $element) {
      $scope.searchRestricao;
      $scope.clearSearchRestricao = function() {
        $scope.searchRestricao = '';
      };

      // The md-select directive eats keydown events for some quick select
      // logic. Since we have a search input here, we don't need that logic.
      /* $element.find('#inputFundamento').on('keydown', function (ev) {
        console.log('executando ev.stopPropagation()');
        ev.stopPropagation();
      });*/
    }]);
})();

(function () {
    "use strict";
    controller.$inject = ["$rootScope", "$mdDialog", "ImovelSuspensaoService", "suspensaoDetalharResolve", "$document", "PaginacaoFactory", "MensagensService"];
    angular.module('su-cadastro-imovel').controller('DetalharSuspensaoController', controller);

    function controller($rootScope, $mdDialog, ImovelSuspensaoService, suspensaoDetalharResolve, $document, PaginacaoFactory, MensagensService) {

        var vm = this;

        vm.suspensao = suspensaoDetalharResolve;
        vm.ripsCancelados = [];
        vm.ripsAtivos = [];
        vm.restricoes = ImovelSuspensaoService.getRestricoes().query();
        vm.ordenacao = null;
        vm.paginacao = PaginacaoFactory.getDefault();

        function init() {
            vm.suspensao.vigencia = new Date(vm.suspensao.vigencia);

            angular.forEach(vm.suspensao.rips, function (rip) {
                if (rip.ativo) {
                    vm.ripsAtivos.push(rip);
                } else {
                    vm.ripsCancelados.push(rip);
                }

                vm.paginacao.totalElements = vm.ripsCancelados.length;
            });
        }

        vm.cancelarSuspensao = function (ev, item) {
            if (!$rootScope.usuarioLogado.permissoes.includes("SUSPENSAO.CANCELAR")) {
                MensagensService.exibirMensagemErro("Você não possui permissão para Cancelar Suspensão");
                vm.ripsAtivos.push(item);
                return;
            }

            var rips = [];
            rips.push(item);

            $mdDialog.show({
                controller: "CancelarSuspensaoImovelController",
                controllerAs: "cancelarSuspensaoCtrl",
                templateUrl: 'scripts-cadastro-imovel/pages/suspensao/view/dialog-cancelar-suspencao-imovel.html',
                parent: $document[0].body,
                targetEvent: ev,
                clickOutsideToClose: true,
                bindToController: true,
                locals: {
                    isDetail: false,
                    suspensao: vm.suspensao.id,
                    rips: rips
                }
            }).then(function (retorno) {
                MensagensService.exibirMensagemSucesso("Suspensão cancelada com sucesso");
                item.ativo = false;
                item.cancelamentoSuspensaoImovel = retorno;
                vm.ripsCancelados.push(item);
                vm.paginacao.totalElements = vm.ripsCancelados.length;
            }, function () {
                vm.ripsAtivos.push(item);
            });
        };

        vm.detalharCancelamento = function (ev, item) {
            $mdDialog.show({
                controller: "CancelarSuspensaoImovelController",
                controllerAs: "cancelarSuspensaoCtrl",
                templateUrl: 'scripts-cadastro-imovel/pages/suspensao/view/dialog-cancelar-suspencao-imovel.html',
                parent: $document[0].body,
                targetEvent: ev,
                clickOutsideToClose: true,
                bindToController: true,
                locals: {
                    isDetail: true,
                    cancelamento: item.cancelamentoSuspensaoImovel
                }
            });
        };

        init();
    }
})();

(function () {
    'use strict';
    ConsultarSuspencaoImovelController.$inject = ["$rootScope", "$mdDialog", "$document", "SuspensaoPesquisa", "ImovelSuspensaoService", "$state", "PaginacaoFactory", "MensagensService", "escopoCompartilhadoService", "camposAjuda"];
    angular.module('su-cadastro-imovel').controller('ConsultarSuspencaoImovelController', ConsultarSuspencaoImovelController);

    function ConsultarSuspencaoImovelController($rootScope, $mdDialog, $document, SuspensaoPesquisa, ImovelSuspensaoService, $state, PaginacaoFactory, MensagensService, escopoCompartilhadoService, camposAjuda) {

        var vm = this;

  		vm.forms = {
            consultarSuspencaoImovel: undefined
        };
  		vm.filtroConsulta = {};
        vm.tiposSuspensao = ImovelSuspensaoService.getTiposSuspensao().query();
        vm.resultadoConsulta = [];
        vm.paginacao = PaginacaoFactory.getDefault();
        vm.ordenacao = null;

    	vm.page = function() {
        	buscar();
        };

        escopoCompartilhadoService.setObjetos('hints', camposAjuda);

        function isNumeroProcessoPreenchido() {
            var numeroProcesso = vm.filtroConsulta.numeroProcesso;
            if (angular.isDefined(numeroProcesso) && numeroProcesso !== null) {
                return numeroProcesso.trim() !== "";
            }
            return false;
        }

        function isRipPreenchido() {
            var rip = vm.filtroConsulta.rip;
            if(angular.isDefined(rip) && rip !== null) {
            	return rip.trim() !== "";
        	}
        	return false;
        }

        function buscar() {
            SuspensaoPesquisa.consultar(vm.filtroConsulta, vm.paginacao.page, vm.paginacao.limit).then(
                function(result) {
                    if(result.data.content.length > 0){
                        vm.paginacao.totalElements = result.data.totalElements;
                        vm.resultadoConsulta = result.data.content;
                    }else{
                        MensagensService.exibirMensagemErro("Nenhuma suspensão encontrada");
                        vm.resultadoConsulta = [];
                    }
                }
            );
        }

        vm.pesquisar = function () {
	        if(isNumeroProcessoPreenchido() || isRipPreenchido()){
                buscar();
		    }else{
	        	MensagensService.exibirMensagemErro("Necessário informar o Nº do Processo ou o RIP");
	    	}
        };

     	vm.limpar = function() {
        	vm.filtroConsulta = {};
        	vm.forms.consultarSuspencaoImovel.$setPristine();
        	vm.forms.consultarSuspencaoImovel.$setUntouched();
        };

        vm.showCancelarSuspensao = function(ev, item) {
            if (!$rootScope.usuarioLogado.permissoes.includes("SUSPENSAO.CANCELAR")) {
                MensagensService.exibirMensagemErro("Você não possui permissão para Cancelar Suspensão");
                return;
            }
            $mdDialog.show({
                controller: "CancelarSuspensaoImovelController",
                controllerAs: "cancelarSuspensaoCtrl",
                templateUrl: 'scripts-cadastro-imovel/pages/suspensao/view/dialog-cancelar-suspencao-imovel.html',
                parent: $document[0].body,
                targetEvent: ev,
                clickOutsideToClose:true,
                bindToController: true,
                locals: {
                    isDetail: false,
                    suspensao: item.id,
                    rips: item.rips
                }
            }).then(function () {
                MensagensService.exibirMensagemSucesso("Suspensão cancelada com sucesso");
                buscar();
            });
        };

        vm.irParaDetalhamento = function (idSuspensao) {
            $state.go("INCORPORACAO.detalharSuspensao", {id: idSuspensao});
        };
    }
})();


(function () {
    "use strict";
    controller.$inject = ["MotivosSuspensao", "$mdDialog", "ImovelSuspensaoService", "MensagensService"];
    angular.module("su-cadastro-imovel").controller("CancelarSuspensaoImovelController", controller);

    function controller(MotivosSuspensao, $mdDialog, ImovelSuspensaoService, MensagensService) {

        var vm = this;

        vm.suspensao = vm.locals.suspensao;
        vm.rips = vm.locals.rips;
        vm.isDetail = vm.locals.isDetail;
        vm.forms = {
            cancelarSuspensaoImovelForm: undefined
        };
        vm.cancelamentoSuspensao = vm.locals.cancelamento || {};
        vm.motivosSuspensao = MotivosSuspensao.query();
        vm.hoje = new Date();
        vm.amanha = new Date(vm.hoje.getFullYear(), vm.hoje.getMonth(), vm.hoje.getDate() + 1);

        function init() {
            if (vm.cancelamentoSuspensao.dataFimVigencia) {
                vm.cancelamentoSuspensao.dataFimVigencia = new Date(vm.cancelamentoSuspensao.dataFimVigencia);
            }
        }

        vm.fechar = function() {
            $mdDialog.cancel();
        };

        vm.salvar = function() {
            if (vm.forms.cancelarSuspensaoImovelForm.$valid) {
                ImovelSuspensaoService.cancelarSuspensao(preparaCancelamento()).then(function (result) {
                    $mdDialog.hide(result.data);
                });
            } else {
                vm.forms.cancelarSuspensaoImovelForm.$setSubmitted();
                MensagensService.exibirMensagemCamposObrigatorios();
            }
        };

        function preparaCancelamento() {
            return {
                motivo: vm.cancelamentoSuspensao.motivo,
                numeroProcesso: vm.cancelamentoSuspensao.numeroProcesso,
                numeroFolha: vm.cancelamentoSuspensao.numeroFolha,
                dataFimVigencia: vm.cancelamentoSuspensao.dataFimVigencia.getTime(),
                observacao: vm.cancelamentoSuspensao.observacao,
                rips: vm.rips,
                idSuspensao: vm.suspensao
            };
        }

        init();
    }
})();

(function () {
    "use strict";
    angular.module('su-cadastro-imovel').controller("CadastroSuspenderImovelController", ["$rootScope", "$scope", "ImovelSuspensaoService", "$state", "$filter", "$timeout", "verificaObrigatorios", "$mdDialog", "modalService", "NOTIFICADORES", "NotificadorService", "ValidarRips", "MensagensService", "escopoCompartilhadoService", "camposAjuda", "IntegracaoSeiService", function($rootScope,$scope,ImovelSuspensaoService,$state, $filter, $timeout,verificaObrigatorios,$mdDialog, modalService, NOTIFICADORES, NotificadorService, ValidarRips, MensagensService, escopoCompartilhadoService, camposAjuda, IntegracaoSeiService){

        var vm = this,
            hoje = new Date();

        vm.suspensaoImovel = {
            restricoes: []
        };
        vm.ripsSelecionados = [];
        vm.tiposSuspensao = ImovelSuspensaoService.getTiposSuspensao().query();
        vm.motivacoes = ImovelSuspensaoService.getMotivacoes().query();
        vm.restricoes = ImovelSuspensaoService.getRestricoes().query();
        vm.ripsConsulta = "";
        vm.hoje = new Date();
        vm.amanha = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + 1);
        vm.forms = {
            cadastroSuspenderImovel: undefined
        };

        escopoCompartilhadoService.setObjetos('hints', camposAjuda);

        vm.incluirRip = function(){
            var arrayRips = vm.ripsConsulta.split(/[\s,;-]+/);
            if(vm.ripsConsulta.length > 0){
                for (var i = 0; i < arrayRips.length; i++) {
                    arrayRips[i] = arrayRips[i].trim();
                }
                var ripsSerializados = angular.toJson(arrayRips);
                var restricoesSerializadas = angular.toJson(vm.suspensaoImovel.restricoes);
                ValidarRips.validar({
                    rips: ripsSerializados,
                    restricoes: restricoesSerializadas
                }).$promise.then(
                    function(result) {
                        if(result.ripsInvalidos.length > 0){
                            var ripsInvalidosMsg = "O(s) RIP(s) informado(s) não é(são) válido(s): ";
                            for (var i = 0; i < result.ripsInvalidos.length; i++) {
                                ripsInvalidosMsg += (i > 0) ? ", " + result.ripsInvalidos[i] : result.ripsInvalidos[i];
                            }
                            MensagensService.exibirMensagemErro(ripsInvalidosMsg);
                        }

                        angular.forEach(result.ripsValidos, function(rip){
                            if (!vm.ripsSelecionados.includes(rip)) {
                                vm.ripsSelecionados.push(rip);
                            }
                        });

                        vm.ripsConsulta = "";
                    }
                );
            }
        };


        vm.goConsulta = function() {
            $state.go('INCORPORACAO.consultarSuspensao');
        };

        vm.tratamentoRip = function () {
          if (vm.ripsConsulta!= null && vm.ripsConsulta.length < 9) {
            vm.ripsConsulta =  ('00000000' + vm.ripsConsulta).slice(-8) ;
          }

          return true;
        }

        vm.temPermissoes = function() {
            if (!$rootScope.usuarioLogado.permissoes.includes("SUSPENSAO.CADASTRAR")) {
                MensagensService.exibirMensagemErro("Você não possui permissão para Cadastrar Suspensão");
                return false;
            }
            for (var i = 0; i < vm.suspensaoImovel.restricoes.length; i++) {
                if (!$rootScope.usuarioLogado.permissoes.includes(vm.suspensaoImovel.restricoes[i].permissao)) {
                    MensagensService.exibirMensagemErro("Você não possui permissão para Cadastrar Suspensão com esta Restrição: " + vm.suspensaoImovel.restricoes[i].permissao);
                    return false;
                }
            }
            return true;
        }

        vm.salvar = function() {
            if(!vm.temPermissoes()){
                return;
            }

            if(vm.forms.cadastroSuspenderImovel.$valid && vm.ripsSelecionados.length > 0) {

                var suspensaoCopy = angular.copy(vm.suspensaoImovel);

                if (angular.isDefined(suspensaoCopy.vigencia) && suspensaoCopy.vigencia !== "") {
                    suspensaoCopy.vigencia = suspensaoCopy.vigencia.getTime();
                }
                if (angular.isDefined(suspensaoCopy.dataFimVigencia) && suspensaoCopy.dataFimVigencia !== "") {
                    suspensaoCopy.dataFimVigencia = suspensaoCopy.dataFimVigencia.getTime();
                }

                var cadastroSuspensao = {
                    suspensaoImovel: suspensaoCopy,
                    rips: vm.ripsSelecionados
                };

                var cadastroSuspensaoSerializado = angular.toJson(cadastroSuspensao);

                ImovelSuspensaoService.save(cadastroSuspensaoSerializado).then(function () {
                    MensagensService.exibirMensagemSucesso("Suspensão gravada com sucesso");
                    vm.goConsulta();
                }, function (error) {
                    if (error !== null)
                        MensagensService.exibirMensagemErro(error.message);
                });
            } else {
                MensagensService.exibirMensagemCamposObrigatorios();

                vm.forms.cadastroSuspenderImovel.$setSubmitted();
            }
        };

        function onlyNumbers(string) {
            if (!string) {
              return '';
            }
            return string.replace(/[^0-9]/g, '');
          }

        vm.buscaProcessoSEISuspensao = function() {
            vm.exibeMengSei = false;
            vm.suspensaoImovel.linkProcessoSei = null;
            if (vm.suspensaoImovel.numeroProcesso != null &&
                vm.suspensaoImovel.numeroProcesso != undefined &&
                vm.suspensaoImovel.numeroProcesso.trim() !== '' &&
                vm.suspensaoImovel.tipoProcessoSei === 'processoExistente') {

                IntegracaoSeiService.recuperarProcedimentoSei(
                    onlyNumbers(vm.suspensaoImovel.numeroProcesso),'ES') // Qualquer UF funciona, aparentemente
                .then(function (resp) {
                    if(resp.data.mensagemErro != null) {
                        vm.resetNumeroProcessoSuspensao();
                        vm.exibeMengSei = true;
                        MensagensService.exibirMensagemErro(
                        resp.data.mensagemErro + ' A ausência de integração não impede o cadastro da suspensão.'
                    );
                    } else {
                    vm.suspensaoImovel.linkProcessoSei = resp.data.linkAcesso;
                    if(vm.suspensaoImovel.numeroProcesso !== null){
                        MensagensService.exibirMensagemSucesso(' Processo encontrado no SEI.');
                    }
                    vm.exibeMengSei = false;
                    }
                })
                .catch(function (err) {
                    console.log(err)
                    vm.suspensaoImovel.linkProcessoSei = null;
                    vm.exibeMengSei = true;
                });
            }
        }

        vm.resetNumeroProcessoSuspensao = function() {
            vm.suspensaoImovel.numeroProcesso = null;
            vm.suspensaoImovel.linkProcessoSei = null;
            vm.exibeMengSei = false;
        }

    }]);

})();

(function () {
    'use strict';
    angular.module('su-cadastro-imovel')
        .factory("PendenciaImovelService", ["$http", "urlBase", function($http, urlBase){
            function buscarTodos(filtro, pagina, limite) {
                var parametros = {
                    imovelPesquisa : filtro,
                    page : pagina,
                    max : limite
                };

                return $http.get(urlBase + 'imovel/pendencia/', {params: parametros});
            }

            return {
                buscarTodos: buscarTodos
            };
        }])
        .factory("SituacaoImovel", ["$resource", "urlBase", function($resource, urlBase){
            return $resource(urlBase + "dominio/situacao-imovel/all");
        }])

        .service('ContadorService', ["$resource", function($resource){
            var contador = 0;

            this.getContador = function() {
                return contador;
            }

            this.setContador = function(cont){
                contador = cont;
            }
        }])

        .factory("TerrenoImovelService", ["$resource", "urlBase", function($resource, urlBase){
            return $resource(urlBase + "imovel/detalhar/terreno/:id", {id : '@id'}, {
                get : {
                    method : 'GET',
                    transformRequest: function (data) {
                        return angular.toJson(data);
                    }
                }
            });
        }]);

})();

(function () {
  'use strict';
  angular
    .module('su-cadastro-imovel')
    .controller('PendenciaImovelController',  ["$rootScope", "$mdDialog", "Homologado", "$state", "imovelResolve", "CartografiaServiceInc", "MensagensService", "ArquivoService", "ciMapaLayersFactory", "ciMapaViewFactory", "CartorioService", "homologarEnviarDestinacaoService", "$anchorScroll", "$location", function (
      $rootScope,
      $mdDialog,
      Homologado,
      $state,
      imovelResolve,
      CartografiaServiceInc,
      MensagensService,
      ArquivoService,
      ciMapaLayersFactory,
      ciMapaViewFactory,
      CartorioService,
      homologarEnviarDestinacaoService,
      $anchorScroll,
      $location
    ) {
      var vm = this,
        WGS84_WEB_MERCATOR = 3857;

      vm.imovelOriginal = imovelResolve.imovelOriginal;
      vm.imovelAlterado = imovelResolve.imovelAlterado;
      vm.todosImoveisDaUf = imovelResolve.todosImoveisDaUf;

      ciMapaLayersFactory.init(true);
      ciMapaViewFactory.init();

      vm.CONSTANTES_IMOVEL_RECUSADO = {
        "TIPO_IMOVEL": 'tipoImovel',
        "MAPA_IMOVEL": 'mapaImovel',
        "COORDENADAS_PONTO": 'coordenadasPonto',
        "ENDERECO": 'endereco',
        "DETALHES_IMOVEL": 'detalhesImovel',
        "DADOS_INCORPORACAO": 'dadosIncorporacao',
        "FOTOS_IMOVEL_GERAL": 'fotosImovelGeral',
        "DADOS_CARTORIAIS": 'dadosCartoriais',
        "DADOS_CADASTRAIS": 'dadosCadastrais',
        "RESTRICOES_USO_OCUPACAO": 'restricoesUsoOcupacao',
        "ARQUIVOS_COMPLEMENTARES_TERRENO": 'arquivosComplementaresTerreno',
        "PROPRIETARIO_TITULAR": 'proprietarioTitular',
        "PROPRIETARIO_ANTERIOR": 'proprietarioAnterior',
        "UNIDADE_AUTONOMA": 'unidadeAutonoma',
        "UNIDADE_AUTONOMA_PLANTAS": 'unidadeAutonomaPlantas',
        "FOTOS_UNIDADE_AUTONOMA": 'fotosUnidadeAutonoma',
        "UNIDADE_AUTONOMA_EDIFICACAO": 'unidadeAutonomaEdificacao',
        "UNIDADE_AUTONOMA_ACESSIBILIDADE": 'unidadeAutonomaAcessibilidade',
        "UNIDADE_AUTONOMA_EDIFICACAO_DADOS_MEDIDOR": 'unidadeAutonomaEdificacaoDadosMedidor',
        "UNIDADE_AUTONOMA_EDIFICACAO_DOCUMENTACAO": 'unidadeAutonomaEdificacaoDocumentacao',
        "MATRICULA_TRANSCRICAO": 'matriculaTranscricao',
        "DOCUMENTO_CARTORIAL": 'documentoCartorial',
      }

      vm.coordenadasImovelOriginal = null;
      vm.coordenadasImovelAlterado = null;
      vm.isBenfeitoria = false;
      vm.pendenciaImovelForm = {};
      //vm.imovelRecusado = null;
      vm.isDetalhamento = false;
      vm.tituloPagina = '';
      vm.titulosAquisitivos = [];
      vm.matriculas = [];
      vm.benfeitorias = [];
      vm.exibeDadosLocalizacao = true;

      function init() {
        vm.coordenadasImovelOriginal = infoCoordenadas();
        vm.coordenadasImovelAlterado = infoCoordenadas();
        vm.isBenfeitoria =
          vm.imovelOriginal.tipoImovel.id === 2 ||
          vm.imovelOriginal.tipoImovel.id === 3;

        if (vm.imovelAlterado.sistemaReferencia) {
          if (vm.imovelAlterado.sistemaReferencia.codigo) {
            atualizarInformacoesCoordenadas(
              vm.coordenadasImovelAlterado,
              vm.imovelAlterado.sistemaReferencia.codigo,
              vm.imovelAlterado.pontoImovel.coordinates
            );

            if (
              vm.isCadastroAlterado() &&
              vm.imovelOriginal.sistemaReferencia.codigo
            ) {
              atualizarInformacoesCoordenadas(
                vm.coordenadasImovelOriginal,
                vm.imovelOriginal.sistemaReferencia.codigo,
                vm.imovelOriginal.pontoImovel.coordinates
              );
            }
          }
        }

        for (var i = 0; i < vm.imovelOriginal.incorporacao.matriculas.length; i++) {
          vm.imovelOriginal.incorporacao.matriculas[i].cartorio = {};
          vm.imovelOriginal.incorporacao.matriculas[i].cartorio.nome = '...';
          preparaCartorio(vm.imovelOriginal, vm.imovelOriginal.incorporacao.matriculas[i].cartorioID, i);
        }

        for (var i = 0; i < vm.imovelAlterado.incorporacao.matriculas.length; i++) {
          vm.imovelAlterado.incorporacao.matriculas[i].cartorio = {};
          vm.imovelAlterado.incorporacao.matriculas[i].cartorio.nome = '...';
          preparaCartorio(vm.imovelAlterado, vm.imovelAlterado.incorporacao.matriculas[i].cartorioID, i);
        }

        if (vm.imovelOriginal.incorporacao.certidoesCartoriais.length > 0) {
          for (var i = 0; i < vm.imovelOriginal.incorporacao.certidoesCartoriais.length; i++) {
            preparaCartorioCertidoes(vm.imovelOriginal, vm.imovelOriginal.incorporacao.certidoesCartoriais[i].cartorio, i);
          }
        }

        if (vm.imovelAlterado.incorporacao.certidoesCartoriais.length > 0) {
          for (var i = 0; i < vm.imovelAlterado.incorporacao.certidoesCartoriais.length; i++) {
            preparaCartorioCertidoes(vm.imovelAlterado, vm.imovelAlterado.incorporacao.certidoesCartoriais[i].cartorio, i);
          }
        }

        gerarUrlArquivosComplementares();
        gerarUrlTitulos();
        gerarUrlDocumentoCadastralTerreno();
        gerarUrlDocumentoCartorialTerreno();
        gerarUrlDocumentosCartoriais();
        gerarUrlDocumentosUnidadeAutonoma();
        gerarUrlPlantasUnidadeAutonoma();
        extrairTitulosAquisitivos();
        extrairMatriculas();
        extrairBenfeitorias();
        gerarUrlDocumentosBenfeitoria();
        gerarUrlPlantasBenfeitoria();
      }

      function preparaCartorio(model, cartorioID, index) {
        CartorioService.pesquisaCartorioPorId
          .query({ id: cartorioID })
          .$promise.then(function (data) {
            for (var i = 0; i < model.incorporacao.matriculas.length; i++) {
              if (i == index) {
                model.incorporacao.matriculas[i].cartorio = data;
                return;
              }
            }
          });
      }

      function preparaCartorioCertidoes(model, cartorioID, index) {
        CartorioService.pesquisaCartorioPorId
          .query({ id: cartorioID })
          .$promise.then(function (data) {
            for (var i = 0; i < model.incorporacao.certidoesCartoriais.length; i++) {
              if (i == index) {
                model.incorporacao.certidoesCartoriais[i].cartorioRecuperado = data;
                return;
              }
            }
          });
      }

      vm.isCadastroAlterado = function () {
        return !!vm.imovelOriginal.rip;
      };

      function extrairBenfeitorias() {
        if (vm.isBenfeitoria) {
            if(vm.imovelOriginal.benfeitorias != null && vm.imovelOriginal.benfeitorias != undefined) {
                for (var i = 0; i < vm.imovelOriginal.benfeitorias.length; i++) {
                    var codigo = vm.imovelOriginal.benfeitorias[i].codigo;

                    var benfeitoria = {
                        benfeitoriaOriginal: vm.imovelOriginal.benfeitorias[i],
                        benfeitoriaAlterada: getByAttribute(
                            vm.imovelAlterado.benfeitorias,
                            'codigo',
                            codigo
                        )
                    };

                    vm.benfeitorias.push(benfeitoria);
                }
            }
            if(vm.imovelAlterado.benfeitorias != null && vm.imovelAlterado.benfeitorias != undefined) {
                for (i = 0; i < vm.imovelAlterado.benfeitorias.length; i++) {
                    benfeitoria = {
                        benfeitoriaOriginal: null,
                        benfeitoriaAlterada: vm.imovelAlterado.benfeitorias[i]
                    };
                    codigo = vm.imovelAlterado.benfeitorias[i].codigo;

                    if (!contem(vm.imovelOriginal.benfeitorias, 'codigo', codigo)) {
                        vm.benfeitorias.push(benfeitoria);
                    }
                }
            }
        }
      }

      function extrairTitulosAquisitivos() {
        for (var i = 0; i < vm.imovelOriginal.titulos.length; i++) {
          var nome = vm.imovelOriginal.titulos[i].nome;

          var tituloAquisitivo = {
            tituloOriginal: vm.imovelOriginal.titulos[i],
            tituloAlterado: getByAttribute(
              vm.imovelAlterado.titulos,
              'nome',
              nome
            )
          };

          vm.titulosAquisitivos.push(tituloAquisitivo);
        }

        for (i = 0; i < vm.imovelAlterado.titulos.length; i++) {
          tituloAquisitivo = {
            tituloOriginal: null,
            tituloAlterado: vm.imovelAlterado.titulos[i]
          };

          nome = vm.imovelAlterado.titulos[i].nome;

          if (!contem(vm.imovelOriginal.titulos, 'nome', nome)) {
            vm.titulosAquisitivos.push(tituloAquisitivo);
          }
        }
      }

      function extrairMatriculas() {
        for (
          var i = 0;
          i < vm.imovelOriginal.incorporacao.matriculas.length;
          i++
        ) {
          var numero = vm.imovelOriginal.incorporacao.matriculas[i].numero;

          var matricula = {
            matriculaOriginal: vm.imovelOriginal.incorporacao.matriculas[i],
            matriculaAlterada: getByAttribute(
              vm.imovelAlterado.incorporacao.matriculas,
              'numero',
              numero
            )
          };

          vm.matriculas.push(matricula);
        }

        for (i = 0; i < vm.imovelAlterado.incorporacao.matriculas.length; i++) {
          matricula = {
            matriculaOriginal: null,
            matriculaAlterada: vm.imovelAlterado.incorporacao.matriculas[i]
          };

          numero = vm.imovelAlterado.incorporacao.matriculas[i].numero;

          if (
            !contem(vm.imovelOriginal.incorporacao.matriculas, 'numero', numero)
          ) {
            vm.matriculas.push(matricula);
          }
        }
      }

      function getByAttribute(objectArray, attribute, item) {
        var retorno = null;

        for (var i = 0; i < objectArray.length; i++) {
          if (item === objectArray[i][attribute]) {
            retorno = objectArray[i];
            break;
          }
        }

        return retorno;
      }

      function contem(objectArray, attribute, item) {
        var retorno = false;
          if (objectArray != null && objectArray != undefined) {
              for (var i = 0; i < objectArray.length; i++) {
                  if (item === objectArray[i][attribute]) {
                      retorno = true;
                  }
              }
          }
        return retorno;
      }

      function infoCoordenadas() {
        return {
          latitude: {
            hemisferio: null,
            graus: null,
            minutos: null,
            segundos: null,
            decimal: null
          },
          longitude: {
            hemisferio: null,
            graus: null,
            minutos: null,
            segundos: null,
            decimal: null
          },
          coordenadaE: null,
          coordenadaN: null,
          fusoUTM: null
        };
      }

      function atualizarInformacoesCoordenadas(
        model,
        sistemaReferencia,
        coordenadas
      ) {
        CartografiaServiceInc.transformarGeometria(
          WGS84_WEB_MERCATOR,
          sistemaReferencia,
          coordenadas[0],
          coordenadas[1]
        ).then(function (result) {
          model.longitude.decimal = result.data.coordinates[0];
          model.longitude.hemisferio = model.longitude.decimal < 0 ? 'O' : 'L';

          model.latitude.decimal = result.data.coordinates[1];
          model.latitude.hemisferio = model.latitude.decimal < 0 ? 'S' : 'N';

          var grauDecimal = {
            longitude: {
              decimal: model.longitude.decimal,
              hemisferio: model.longitude.hemisferio
            },
            latitude: {
              decimal: model.latitude.decimal,
              hemisferio: model.latitude.hemisferio
            }
          };

          var gms = CartografiaServiceInc.gdToGms(grauDecimal);

          model.longitude.graus = gms.longitude.graus;
          model.longitude.minutos = gms.longitude.minutos;
          model.longitude.segundos = gms.longitude.segundos;

          model.latitude.graus = gms.latitude.graus;
          model.latitude.minutos = gms.latitude.minutos;
          model.latitude.segundos = gms.latitude.segundos;

          var utm = CartografiaServiceInc.gdToUtm(grauDecimal);

          model.coordenadaE = utm.coordenadaE;
          model.coordenadaN = utm.coordenadaN;
          model.fusoUTM = utm.fusoUTM;
        });
      }

      vm.isDifferent = function (div) {

        if (!vm.isCadastroAlterado()) {
          return false;
        }

        if (vm.isDetalhamento) {
          return true;
        }

        var diferencas = angular.element(div).find('span.ng-binding.span-alterado-component');

        if (angular.element(div)[0].childElementCount === 0) {
          return false;
        }
        if (diferencas.length !== 0)
          return false;
        else
          return true;

      }

      vm.toggle = function (value) {
        var idx = vm.imovelRecusado.camposRecusados.indexOf(value);

        if (idx > -1) {
          vm.informacoesRecusada.checkAll = false;
          vm.imovelRecusado.camposRecusados.splice(idx, 1);
        } else {
          vm.imovelRecusado.camposRecusados.push(value);
        }
        if (vm.imovelRecusado.camposRecusados.length == 22) {
          vm.informacoesRecusada.checkAll = true;
        }
      };

      vm.toggleAll = function () {

        // INFORMAR TODOS OS NG-MODEL DE CADA CHECKBOX DA TELA DE PENDENCIA SEGUINDO MESMO PADRAO
        vm.informacoesRecusada.tipoImovel = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.mapaImovel = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.coordenadasPonto = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.endereco = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.detalhesImovel = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.dadosIncorporacao = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.fotosImovelGeral = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.dadosCartoriais = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.dadosCadastrais = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.restricoesUsoOcupacao = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.arquivosComplementaresTerreno = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.proprietarioTitular = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.proprietarioAnterior = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonoma = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.fotosUnidadeAutonoma = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonomaPlantas = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonomaEdificacao = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonomaAcessibilidade = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonomaEdificacaoDadosMedidor = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonomaEdificacaoDocumentacao = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.matriculaTranscricao = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.documentoCartorial = vm.informacoesRecusada.checkAll;

        //CASO ELE CLICK EM RECUSAR TODOS OS DADOS O OBJETO CAMPOS RECUSADOS E LIMPO E DEPOIS ADD TODOS PARA EVITAR
        //DUPLICIDADE NA LISTA
        if (vm.informacoesRecusada.checkAll) {
          vm.imovelRecusado.camposRecusados = [];

          angular.forEach(Object.values(vm.CONSTANTES_IMOVEL_RECUSADO), function (value) {
            vm.imovelRecusado.camposRecusados.push(value);
          });
        } else {
          vm.imovelRecusado.camposRecusados = [];
        }

      };

      vm.formAdress = function (imovel) {
        var endereco = '';

        if (imovel.enderecamento.pais.id !== 1) {
          endereco = imovel.enderecamento.endereco + ', ' + imovel.enderecamento.cidadeExterior + ' - ' + imovel.enderecamento.pais.descricao + ', ' + imovel.enderecamento.codigoPostal;
          return endereco;
        }

        if (imovel.enderecamento.tipoLogradouro) {
          endereco += imovel.enderecamento.tipoLogradouro + ' ';
        }
        if (imovel.enderecamento.logradouro) {
          endereco += imovel.enderecamento.logradouro + ', ';
        }
        if (imovel.numero) {
          endereco += 'Número: ' + imovel.numero + ', ';
        }
        if (imovel.enderecamento.bairro) {
          endereco += imovel.enderecamento.bairro + ', ';
        }
        if (imovel.complemento) {
          endereco += imovel.complemento + ', ';
        }
        if (imovel.enderecamento.municipio) {
          endereco += imovel.enderecamento.municipio + '/' + imovel.enderecamento.uf + ' ';
        }
        if (imovel.enderecamento.pais) {
          endereco += imovel.enderecamento.pais.descricao + ', ';
        }
        if (imovel.enderecamento.cep) {
          endereco += 'CEP: ' + imovel.enderecamento.cep;
        }
        return endereco;


      }

      vm.sizeColspan = function () {
        if (vm.isCadastroAlterado()) {
          return 2;
        }
        return 1;
      }

      vm.sizeColspanFull = function () {
        if (vm.isCadastroAlterado()) {
          return 3;
        }
        return 2;
      }

      vm.isAllChecked = function () {
        return (
          vm.imovelRecusado.camposRecusados.length > 0 &&
          vm.imovelRecusado.camposRecusados.length ===
          angular
            .element('#detalhamentoImovel')
            .find('md-checkbox:not([disabled])').length
        );
      };

      vm.isIndeterminate = function () {
        return (
          vm.imovelRecusado.camposRecusados.length > 0 && !vm.isAllChecked()
        );
      };

      vm.exists = function (campo) {
        return vm.imovelRecusado.camposRecusados.indexOf(campo) > -1;
      };

      vm.desabilitarCheckbox = function (dadoOriginal, dadoAlterado) {
        return (
          vm.isDetalhamento ||
          (vm.isCadastroAlterado() && vm.isEquals(dadoOriginal, dadoAlterado))
        );
      };

      vm.isEquals = function (dadoOriginal, dadoAlterado) {
        if (dadoAlterado instanceof Object) {
          return angular.equals(dadoOriginal, dadoAlterado);
        } else {
          return dadoOriginal == dadoAlterado;
        }
      };

      vm.isPaisBrasil = function () {
        return (
          vm.imovelAlterado.enderecamento.pais &&
          vm.imovelAlterado.enderecamento.pais.id === 1
        );
      };

      vm.isDadoDivergente = function (dadoOriginal, dadoAlterado) {
        return (
          vm.isCadastroAlterado() && !angular.equals(dadoOriginal, dadoAlterado)
        );
      };

      vm.recusarImovel = function () {
        if (
          vm.imovelRecusado.camposRecusados.length > 0 &&
          vm.pendenciaImovelForm.$valid
        ) {
          var confirm = $mdDialog.confirm({
            focusOnOpen: false,
            textContent: 'Deseja recusar este imóvel?',
            cancel: 'Não',
            ok: 'Recusar'
          });

          $mdDialog.show(confirm).then(function () {
            Homologado.recusar(vm.imovelRecusado).then(function () {
              $state.go('INCORPORACAO.pendenciaImovel');
              MensagensService.exibirMensagemSucesso(
                'Imóvel recusado com sucesso.'
              );
            });
          });
        } else if (vm.imovelRecusado.camposRecusados.length === 0) {
          MensagensService.exibirMensagemErro(
            'É necessário selecionar a opção recusar de pelo menos um dos campos.'
          );
        } else {
          MensagensService.exibirMensagemCamposObrigatorios();
        }
      };

      vm.fechar = function () {
        if (vm.isDetalhamento && !!$state.previousState.name) {
          $state.go($state.previousState.name, $state.previousState.params);
        } else {
          $state.go('INCORPORACAO.pendenciaImovel');
        }
      };

      vm.disableHomologacao = function () {
        return vm.imovelRecusado.camposRecusados.length > 0;
      };

      function gerarUrlArquivosComplementares() {
        angular.forEach(vm.imovelAlterado.arquivosComplementaresLista, function (
          arquivoComplementar
        ) {
          arquivoComplementar.arquivo.url = ArquivoService.getUrlFromBase64(
            arquivoComplementar.arquivo.arquivo
          );
        });

        angular.forEach(vm.imovelOriginal.arquivosComplementaresLista, function (
          arquivoComplementar
        ) {
          arquivoComplementar.arquivo.url = ArquivoService.getUrlFromBase64(
            arquivoComplementar.arquivo.arquivo
          );
        });
      }

      function gerarUrlDocumentosCartoriais() {
        angular.forEach(
          vm.imovelAlterado.incorporacao.certidoesCartoriais,
          function (documento) {
            documento.arquivo.url = ArquivoService.getUrlFromBase64(
              documento.arquivo.arquivo
            );
          }
        );

        angular.forEach(
          vm.imovelOriginal.incorporacao.certidoesCartoriais,
          function (documento) {
            documento.arquivo.url = ArquivoService.getUrlFromBase64(
              documento.arquivo.arquivo
            );
          }
        );
      }

      function gerarUrlDocumentoCartorialTerreno() {
        if (vm.imovelAlterado.terreno.arquivoCartorial) {
          vm.imovelAlterado.terreno.arquivoCartorial.url = ArquivoService.getUrlFromBase64(
            vm.imovelAlterado.terreno.arquivoCartorial.arquivo);
        }

        if (vm.imovelOriginal.terreno.arquivoCartorial) {
          vm.imovelOriginal.terreno.arquivoCartorial.url = ArquivoService.getUrlFromBase64(
            vm.imovelOriginal.terreno.arquivoCartorial.arquivo);
        }
      }

      function gerarUrlDocumentoCadastralTerreno() {
        if (vm.imovelAlterado.terreno.arquivoCadastral) {
          vm.imovelAlterado.terreno.arquivoCadastral.url = ArquivoService.getUrlFromBase64(
            vm.imovelAlterado.terreno.arquivoCadastral.arquivo);
        }

        if (vm.imovelOriginal.terreno.arquivoCadastral) {
          vm.imovelOriginal.terreno.arquivoCadastral.url = ArquivoService.getUrlFromBase64(
            vm.imovelOriginal.terreno.arquivoCadastral.arquivo);
        }
      }

      function gerarUrlTitulos() {
        angular.forEach(
          vm.imovelAlterado.titulos,
          function (documento) {
            documento.url = ArquivoService.getUrlFromBase64(
              documento.arquivo
            );
          }
        );

        angular.forEach(
          vm.imovelOriginal.titulos,
          function (documento) {
            documento.url = ArquivoService.getUrlFromBase64(
              documento.arquivo
            );
          }
        );
      }

        function gerarUrlDocumentosBenfeitoria() {
            if (vm.benfeitorias.length !== 0) {
                angular.forEach(vm.benfeitorias, function (benfeitoria) {
                    if (benfeitoria.benfeitoriaAlterada && benfeitoria.benfeitoriaAlterada.edificacao) {
                        angular.forEach(
                            benfeitoria.benfeitoriaAlterada.edificacao.documentacao,
                            function (documento) {
                                if (documento.arquivo) {
                                    documento.arquivo.url = ArquivoService.getUrlFromBase64(
                                        documento.arquivo.arquivo
                                    );
                                }
                            }
                        );
                    }

                    if (benfeitoria.benfeitoriaOriginal && benfeitoria.benfeitoriaOriginal.edificacao) {
                        angular.forEach(
                            benfeitoria.benfeitoriaOriginal.edificacao.documentacao,
                            function (documento) {
                                if (documento.arquivo) {
                                    documento.arquivo.url = ArquivoService.getUrlFromBase64(
                                        documento.arquivo.arquivo
                                    );
                                }
                            }
                        );
                    }
                });
            }
        }

      function gerarUrlPlantasBenfeitoria() {
        if (vm.benfeitorias.length !== 0) {
          angular.forEach(vm.benfeitorias, function (benfeitoria) {
           if(benfeitoria.benfeitoriaAlterada) {
               angular.forEach(
                   benfeitoria.benfeitoriaAlterada.plantas,
                   function (plantaArq) {
                       if (plantaArq.planta) {
                           plantaArq.url = ArquivoService.getUrlFromBase64(
                               plantaArq.planta
                           );
                       }
                   }
               );
           }
              if (benfeitoria.benfeitoriaOriginal) {
                  angular.forEach(
                      benfeitoria.benfeitoriaOriginal.plantas,
                      function (plantaArq) {
                          if (plantaArq.planta) {
                              plantaArq.url = ArquivoService.getUrlFromBase64(
                                  plantaArq.planta
                              );
                          }
                      }
                  );
              }
          });
        }
      }

      function gerarUrlDocumentosUnidadeAutonoma() {
        if (
          vm.imovelAlterado.unidadeAutonoma &&
          vm.imovelAlterado.unidadeAutonoma.edificacao
        ) {
          angular.forEach(
            vm.imovelAlterado.unidadeAutonoma.edificacao.documentacao,
            function (documento) {
              if (documento.arquivo) {
                documento.arquivo.url = ArquivoService.getUrlFromBase64(
                  documento.arquivo.arquivo
                );
              }
            }
          );

          angular.forEach(
            vm.imovelOriginal.unidadeAutonoma.edificacao.documentacao,
            function (documento) {
              if (documento.arquivo) {
                documento.arquivo.url = ArquivoService.getUrlFromBase64(
                  documento.arquivo.arquivo
                );
              }
            }
          );
        }
      }

      function gerarUrlPlantasUnidadeAutonoma() {
        if (vm.imovelAlterado.unidadeAutonoma) {
          angular.forEach(vm.imovelAlterado.unidadeAutonoma.plantas, function (
            planta
          ) {
            planta.arquivo.url = ArquivoService.getUrlFromBase64(
              planta.arquivo.arquivo
            );
          });

          angular.forEach(vm.imovelOriginal.unidadeAutonoma.plantas, function (
            planta
          ) {
            planta.arquivo.url = ArquivoService.getUrlFromBase64(
              planta.arquivo.arquivo
            );
          });
        }
      }

      function iniciaObjetoImovelRecusado() {
        vm.informacoesRecusada = {};
        vm.informacoesRecusada.tipoImovel = false;
        vm.informacoesRecusada.checkAll = false;
        vm.informacoesRecusada.mapaImovel = false;
        vm.informacoesRecusada.coordenadasPonto = false;
        vm.informacoesRecusada.endereco = false;
        vm.informacoesRecusada.detalhesImovel = false;
        vm.informacoesRecusada.dadosIncorporacao = false;
        vm.informacoesRecusada.fotosImovelGeral = false;
        vm.informacoesRecusada.dadosCartoriais = false;
        vm.informacoesRecusada.dadosCadastrais = false;
        vm.informacoesRecusada.restricoesUsoOcupacao = false;
        vm.informacoesRecusada.arquivosComplementaresTerreno = false;
        vm.informacoesRecusada.proprietarioTitular = false;
        vm.informacoesRecusada.proprietarioAnterior = false;
        vm.informacoesRecusada.unidadeAutonoma = false;
        vm.informacoesRecusada.unidadeAutonomaPlantas = false;
        vm.informacoesRecusada.fotosUnidadeAutonoma = false;
        vm.informacoesRecusada.unidadeAutonomaEdificacao = false;
        vm.informacoesRecusada.unidadeAutonomaAcessibilidade = false;
        vm.informacoesRecusada.unidadeAutonomaEdificacaoDadosMedidor = false;
        vm.informacoesRecusada.unidadeAutonomaEdificacaoDocumentacao = false;
        vm.informacoesRecusada.matriculaTranscricao = false;
        vm.informacoesRecusada.documentoCartorial = false;
      }

      vm.boolToString = function (boolean) {
        if (angular.isDefined(boolean)) {
          return boolean ? 'Sim' : 'Não';
        }
      };

      vm.showHomologarImovel = function (ev) {
        var confirm = $mdDialog.confirm({
          focusOnOpen: false,
          textContent: 'Deseja realmente homologar este imóvel?',
          targetEvent: ev,
          cancel: 'Não',
          ok: 'Homologar'
        });

        if (
          $rootScope.usuarioLogado.jurisdicoes.includes(
            vm.imovelAlterado.enderecamento.uf
          )
        ) {
          $mdDialog.show(confirm).then(
            function () {
              $mdDialog.show(
                $mdDialog.alert({
                  templateUrl:
                    'scripts-cadastro-imovel/pages/imovel/view/modals/progress.tmpl.html'
                })
              );
              vm.imovelAlterado.imovelRecusado = null;

              Homologado.save(vm.imovelAlterado).then(function (data) {
                MensagensService.exibirMensagemSucesso(
                  'Imóvel homologado com sucesso Rip: ' + data.rip
                );
                vm.imovelOriginal.rip = data.rip;
                $mdDialog.hide();
                vm.enviarDocumentosSei();
              });
            },
            function () {
              $mdDialog.cancel();
            }
          );
        } else {
          MensagensService.exibirMensagemErro(
            'O imóvel deve estar contido nas jurisdições do usuário.'
          );
        }
      };

      vm.goToDadosLocalizacao = function () {
        $anchorScroll();
        $location.hash('dadosDoImovel');
        vm.exibeDadosLocalizacao = true;
      };

        vm.enviarDocumentosSei = function () {
            $mdDialog.show({
                controller: enviarDocumentosSeiDialogController,
                templateUrl:
                    'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-enviar-documentos-sei.html',
                parent: angular.element(document.body),
                clickOutsideToClose: false,
                locals: {
                    imovel: vm.imovelOriginal
                }
            });
        };

        var enviarDocumentosSeiDialogController = enviarDocumentosSeiDialogController;

        enviarDocumentosSeiDialogController.$inject = [
            '$scope',
            '$http',
            '$mdDialog',
            'imovel',
            '$timeout'
        ];
        function enviarDocumentosSeiDialogController(
            $scope,
            $http,
            $mdDialog,
            imovel,
            $timeout
        ) {
            $scope.imovel = imovel;
            $scope.envioSei = {};
            $scope.envioSei.despachoConclusao = false;
            $scope.envioSei.folhaRosto = false;
            $scope.envioSei.memorialDescritivo = false;
            $scope.envioSei.shapefileTerrenoCartorial = false;
            $scope.envioSei.shapefileTerrenoCadastral = false;
            $scope.envioSei.shapefileBenfeitorias = false;
            $scope.envioSei.shapefileEdificacao = false;
            $scope.envioSei.tituloAquisitivo = false;
            $scope.envioSei.documentoCartorial = false;
            $scope.envioSei.arquivosComplementares = false;
            $scope.envioSei.documentacaoEdificacao = false;
            $scope.envioSei.arquivoPlanta = false;
            $scope.showEdificacaoAutonoma = false;
            $scope.showEdificacaoBenfeitoria = false;
            $scope.showPlantaBenfeitoria = false;
            $scope.showPlantaAutonoma = false;

            apresentaDocumentacaoEdificacao();
            apresentaDocumentacaoBenfeitoria();
            apresentaPlantasBenfeitoria();
            apresentaPlantaEdificacao();

            $scope.fechar = function () {
                $timeout(function () {
                    $state.go('INCORPORACAO.pendenciaImovel');
                }, 1000);
                $mdDialog.cancel();
            };

            function apresentaDocumentacaoBenfeitoria () {
                if (imovel.benfeitorias != null && imovel.benfeitorias != undefined) {
                    angular.forEach(imovel.benfeitorias, function (benfeitoria) {
                        if (benfeitoria.edificacao != null && benfeitoria.edificacao != undefined) {
                            angular.forEach(benfeitoria.edificacao.documentacao, function (documento) {
                                if (documento.arquivo != null && documento.arquivo != undefined) {
                                    $scope.showEdificacaoBenfeitoria = true;
                                }
                            });
                        }
                    });
                }
            }


            function apresentaDocumentacaoEdificacao () {
                if (imovel.unidadeAutonoma != null && imovel.unidadeAutonoma != undefined) {
                    angular.forEach(imovel.unidadeAutonoma.edificacao.documentacao, function (value) {
                        if (value.arquivo != null && value.arquivo != undefined) {
                            $scope.showEdificacaoAutonoma = true;
                        }
                    });
                }
            }

            function apresentaPlantaEdificacao () {
                if (imovel.unidadeAutonoma != null && imovel.unidadeAutonoma != undefined) {
                    angular.forEach(imovel.unidadeAutonoma.edificacao.plantas, function (value) {
                        if (value.arquivo != null && value.arquivo != undefined) {
                            $scope.showPlantaAutonoma = true;
                        }
                    });
                }
            }

            function apresentaPlantasBenfeitoria () {
                if (imovel.benfeitorias != null && imovel.benfeitorias != undefined) {
                    angular.forEach(imovel.benfeitorias, function (benfeitoria) {
                        if (benfeitoria.plantas != null && benfeitoria.plantas != undefined) {
                            angular.forEach(benfeitoria.plantas, function (planta) {
                                if (planta.planta != null && planta.planta != undefined) {
                                    $scope.showPlantaBenfeitoria = true;
                                }
                            });
                        }
                    });
                }
            }

            $scope.confirmar = function () {
                $scope.envioSei.imovel = $scope.imovel;
                homologarEnviarDestinacaoService.documentoSei($scope.envioSei).then(function () {
                    $mdDialog.cancel();
                    MensagensService.exibirMensagemSucesso("Arquivos enviados para o SEI com sucesso");
                    $timeout(function () {
                        $state.go('INCORPORACAO.pendenciaImovel');
                    }, 1000);
                },function (erro){
                    MensagensService.exibirMensagemErro(erro);
                });
            };
        }
      init();

    }]);
})();

(function () {
  'use strict';

  consultarPendenciaController.$inject = ["$scope", "$location", "$mdDialog", "Dominio", "ectCepService", "UfsService", "PaisesUtilizadosService", "MunicipioService", "TipoCancelImovelPesquisa", "SituacaoImovel", "Cancelamento", "PendenciaImovelService", "SituacaoCadastro", "MAPA_CONSTANTS_INCORPORACAO", "NotificadorService", "MensagensService", "$state", "PaginacaoFactory", "camposAjuda", "escopoCompartilhadoService", "$document", "AcaoExecutasService", "PendenciasService", "CpfCnpjService", "SincronizarService", "CidadesExtService"];
  angular
    .module('su-cadastro-imovel')
    .controller('ConsultarPendenciaController', consultarPendenciaController);

  function consultarPendenciaController(
    $scope,
    $location,
    $mdDialog,
    Dominio,
    ectCepService,
    UfsService,
    PaisesUtilizadosService,
    MunicipioService,
    TipoCancelImovelPesquisa,
    SituacaoImovel,
    Cancelamento,
    PendenciaImovelService,
    SituacaoCadastro,
    MAPA_CONSTANTS_INCORPORACAO,
    NotificadorService,
    MensagensService,
    $state,
    PaginacaoFactory,
    camposAjuda,
    escopoCompartilhadoService,
    $document,
    AcaoExecutasService,
    PendenciasService,
    CpfCnpjService,
    SincronizarService,
    CidadesExtService
  ) {
    var vm = this,
      notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES;

    vm.imovel = {};
    vm.imovelSelecionado = {};
    vm.situacoes = SituacaoImovel.query();
    vm.resultadoPesquisa = [];
    vm.motivos = TipoCancelImovelPesquisa.query();
    vm.tiposTitular = Dominio.tiposProprietarioReal();
    AcaoExecutasService.query().$promise.then(function (result) {
      vm.acoesExecutadas = result;
    });
    vm.pendenciaSelecionada = $location.search().pendencia;
    PendenciasService.getResumoPendencias().query().$promise.then(function (result) {
      vm.pendencias = result;
    });
    vm.paises = PaisesUtilizadosService.query();
    vm.situacaoCadastro = SituacaoCadastro.query();
    vm.ufs = UfsService.UFS;
    vm.municipios = [];
    vm.Cancelamento = Cancelamento;
    vm.imovelPesquisa = { enderecamento: {} };
    vm.paginacao = PaginacaoFactory.getDefault();
    vm.cepPreenchido = false;
    vm.cepValido = false;
    vm.isCepGenerico = false;
    vm.imovelPesquisa.cpfCnpjProprietarios = [];
    vm.exibirImoveisCadastradosEditadosDoUsuario = false;
    vm.tipoRip = 'spunet';
    vm.cidades = [];

    function init() {
      escopoCompartilhadoService.setObjetos('hints', camposAjuda);

      $scope.$watch('consultarPendenciaCtrl.tipoRip', function (tipoRip) {
        if (tipoRip === 'spunet') {
          vm.imovelPesquisa.ripSpiunet = undefined;
        } else {
          vm.imovelPesquisa.rip = undefined;
        }
      });
    }

    vm.progress = function () {
      $mdDialog.show(
        $mdDialog.alert({
          templateUrl:
            'scripts-cadastro-imovel/pages/imovel/view/modals/progress.tmpl.html'
        })
      );
    };

    vm.closeProgress = function () {
      $mdDialog.hide();
    };

    vm.tratamentoRip = function () {
      vm.imovelPesquisa.rip = !vm.imovelPesquisa.rip || !vm.imovelPesquisa.rip.replaceAll('0', '') ?
        undefined : ('00000000' + vm.imovelPesquisa.rip).slice(-8);
      return vm.imovelPesquisa.rip;
    };

    vm.buscaEnderecoPorCep = function () {
      var cep = vm.imovelPesquisa.cep;
      if (cep != undefined && cep.length == 8) {
        vm.progress();
        ectCepService.get({ cep: cep }).$promise.then(
          function (data) {
            data.pais = Dominio.Constantes.Brasil;
            vm.imovelPesquisa.enderecamento = data;
            vm.imovelPesquisa.uf = vm.imovelPesquisa.enderecamento.uf;
            vm.imovelPesquisa.municipio = vm.imovelPesquisa.enderecamento.municipio;
            $scope.$emit('municipioImovelSelecionado', data.codigoIbge);
            vm.isCepGenerico = vm.verificaCEPGenerico(cep);
            vm.cepValido = true;
            vm.closeProgress();
          },
          function () {
            vm.closeProgress();
            vm.imovelPesquisa.uf = '';
            vm.imovelPesquisa.municipio = '';
            vm.cepValido = false;
            MensagensService.exibirMensagemErro('CEP não encontrado');
          }
        );
        vm.cepPreenchido = true;
      } else {
        vm.imovelPesquisa.enderecamento = {};
        vm.imovelPesquisa.enderecamento.pais = Dominio.Constantes.Brasil;
        vm.cepPreenchido = false;
        vm.cepValido = false;
      }
    };

    vm.verificaCEPGenerico = function (cep) {
      return cep.endsWith('000');
    };

    function pesquisar() {
      vm.resultadoPesquisa = [];
      var imovelSerializado = vm.imovelPesquisa;
      delete imovelSerializado.enderecamento.geometria;
      PendenciaImovelService.buscarTodos(
        imovelSerializado,
        vm.paginacao.page,
        vm.paginacao.limit
      ).then(function (result) {
        if (result.data.content.length > 0) {
          vm.paginacao.totalElements = result.data.totalElements;
          vm.resultadoPesquisa = result.data.content;
        } else {
          MensagensService.exibirMensagemErro('Nenhum imóvel encontrado');
        }
      });
    }

    vm.validarFormulario = function () {
      if ($scope.consultarPendenciaImovelForm.$valid) {
        pesquisar();
      } else {
        $scope.consultarPendenciaImovelForm.$setSubmitted();
        MensagensService.exibirMensagemCamposObrigatorios();
      }
    };

    vm.limpar = function () {
      vm.imovelPesquisa = {};
      vm.imovel.situacaoCadastro = null;
      $scope.consultarPendenciaImovelForm.$setPristine();
      $scope.consultarPendenciaImovelForm.$setUntouched();
    };

    vm.page = function () {
      var imovelSerializado = vm.imovelPesquisa;
      return PendenciaImovelService.buscarTodos(
        imovelSerializado,
        vm.paginacao.page,
        vm.paginacao.limit
      ).then(function (result) {
        vm.resultadoPesquisa = result.data.content;
      });
    };

    vm.getTituloPendencia = function (item) {
      var pendenciaMigracao;
      var pendenciaSincronizacao;

      item.pendencias.forEach(function (pendencia) {
        if (pendencia.descricaoPendencia === 'VALIDAR_CADASTRO_SPIUNET') {
          pendenciaMigracao = pendencia;
        }
        if (pendencia.descricaoPendencia === 'SINCRONIZAR_IMOVEL_SPIUNET') {
          pendenciaSincronizacao = pendencia;
        }
      });

      if (!!pendenciaMigracao) {
        return 'Imóvel oriundo da migração de imóveis do sistema SPIUnet, é necessária a validação dos dados desse imóvel.';
      }
      if (!!pendenciaSincronizacao) {
        var erro = !pendenciaSincronizacao.descricaoErro ? '' : ' Erro ao sincronizar: ' + pendenciaSincronizacao.descricaoErro;
        return 'Pendente sincronização com o SPIUnet.' + erro;
      }
      return null;
    };

    vm.showLocalizacaoImovelDialog = function (ev, item) {
      $mdDialog.show({
        controller: localizacaoImovelPendenciaCtrl,
        templateUrl:
          'scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-pendencia/dialog-detalhar-pendencia-imoveis.html',
        parent: angular.element($document[0].body),
        targetEvent: ev,
        clickOutsideToClose: true,
        onComplete: notificaDiretivaMapa,
        locals: {
          imovel: item,
        },
      });
    };

    vm.verLocalizacao = function (ev, item) {
      $mdDialog.show({
        controller: localizacaoImovelPendenciaCtrl,
        templateUrl:
          'scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-localizacao-imovel.html',
        parent: angular.element($document[0].body),
        targetEvent: ev,
        clickOutsideToClose: true,
        locals: {
          imovel: item,
        },
      });
    };

    var notificaDiretivaMapa = function () {
      NotificadorService.notify(
        $scope,
        vm.resultadoPesquisa,
        notifiers.RESULTADOPESQUISARIMOVEL
      );
    };

    var localizacaoImovelPendenciaCtrl = function (
      $scope,
      $http,
      $mdDialog,
      imovel
    ) {
      $scope.imovel = imovel;

      $scope.fechar = function () {
        $mdDialog.cancel();
      };
    };
    localizacaoImovelPendenciaCtrl.$inject = ["$scope", "$http", "$mdDialog", "imovel"];

    vm.irParaDetalharImovel = function (id) {
      $state.go('INCORPORACAO.detalharImovel', { id: id });
    };

    vm.irParaEdicao = function (id) {
      $state.go('INCORPORACAO.alterarCadastro', { id: id, tab: -1 });
    };

    vm.exibeEdicao = function (item) {
      return (
        !isCancelado(item) &&
        !isSuspensoEdicaoCancelamento(item) &&
        isCadastradorAutorizado(item)
      );
    };

    vm.exibeSincronizacao = function (item) {
      return item.dsSpiunet === 'pend. sincronizacao';
    };

    function isCancelado(item) {
      return item.situacaoImovel.id === 3;
    }

    function isSuspensoEdicaoCancelamento(item) {
      return item.isSuspensoEdicaoCancelamento;
    }

    function isCadastradorAutorizado(item) {
      return item.isCadastradorAutorizado;
    }

    vm.pesquisaMunicipio = function (uf) {
      if (uf && uf.sigla) {
        vm.progress();
        vm.municipios = [];
        MunicipioService.TodosMunicipios.query({
          uf: uf.sigla,
        }).$promise.then(function (result) {
          vm.municipios = result;
          vm.closeProgress();
        });
        vm.imovelPesquisa.uf = uf.sigla;
      }
    };

    vm.apresentaBotao = function () {
      if (vm.cepPreenchido === true) {
        return vm.isCepGenerico && !vm.cepValido;
      } else {
        return true;
      }
    };
    vm.formataCpfCnpj = function (documento) {
      return CpfCnpjService.formataCpfCnpj(documento);
    };

    $scope.$watch('consultarPendenciaCtrl.imovelPesquisa.exibirImoveisCadastradosEditadosDoUsuario', function (
      value
    ) {
      vm.cpfUsuario = null;
      if (value && $scope.usuarioLogado && $scope.usuarioLogado.cpf) {
        vm.imovelPesquisa.cpfUsuario = $scope.usuarioLogado.cpf;
      }
    });

    vm.selecionaMunicipio = function () {
      if (vm.imovelPesquisa.municipio && vm.imovelPesquisa.municipio.nome) {
        vm.imovelPesquisa.municipio = vm.imovelPesquisa.municipio.nome;
      }
    };

    vm.buscaCidadesPorPais = function () {
      vm.imovelPesquisa.cidadeExterior = null;
      if ( vm.imovelPesquisa.pais !=null){
        var pais = vm.imovelPesquisa.pais;
        if (pais != 1 && pais != null) {
          CidadesExtService.query({id_pais: pais}).$promise.then(function (result) {
              vm.cidades = result;
            });
        }
      }
    };

    init();
  }
})();

(function () {
    /* global ol */
    MapaService.$inject = ["NotificadorService", "MAPA_CONSTANTS_INCORPORACAO"];
    angular.module("su-cadastro-imovel").service("MapaService", MapaService);

    function MapaService(NotificadorService, MAPA_CONSTANTS_INCORPORACAO) {

        var vm = this,
            notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES;

        vm.PONTO_IMOVEL = 'pontoImovel';
        vm.GEOMETRIA_TERRENO = 'geometriaTerreno';
        vm.GEOMETRIA_UNIDADE_AUTONOMA = "geometriaUnidadeAutonoma";
        vm.UPDATE_MAPA = 'refreshMap';
        vm.BENFEITORIA = 'benfeitoria';
        vm.UNIDADE_AUTONOMA = "unidadeAutonoma";
        vm.IS_MAP_EMPTY = 'isMapEmpty';
        vm.DESENHAR_BENFEITORIA = 'desenharBenfeitoria';
        vm.REMOVER_BENFEITORIA = 'removerBenfeitoria';
        vm.REGISTRAR_BENFEITORIA = 'registrarBenfeitoria';
        vm.DESENHAR_UNIDADE_AUTONOMA = "desenharUnidadeAutonoma";
        vm.REMOVER_UNIDADE_AUTONOMA = "removerUnidadeAutonoma";
        vm.REGISTRAR_UNIDADE_AUTONOMA = "registrarUnidadeAutonoma";

        vm.observadoresCallbacks = [];

        vm.interacoes = [];
        vm.features = [];
        vm.layers = [];
        vm.sources = [];
        vm.requiredZoom = 13;
        vm.defaultProjection = 'EPSG:4674';

        vm.features.desenhoPonto = new ol.Collection();
        vm.features.desenho = new ol.Collection();

        vm.init = function () {
            vm.loadPrototype();
        };

        vm.addTerreno = function (scope, obj) {
            NotificadorService.notify(scope, obj, notifiers.ADICIONAR_FEATURE_TERRENO);
        };

        vm.removeFeature = function (scope, id) {
            NotificadorService.notify(scope, id, notifiers.REMOVER_FEATURE_BY_ID);
        };

        vm.registrarObservadorCallback = function (name, callback) {
            //console.log(callback, "observador " + name)
            if (!vm.observadoresCallbacks[name]) {
                vm.observadoresCallbacks[name] = [];
            }
            vm.observadoresCallbacks[name].push(callback);
        };

        vm.notificarObservadores = function (name, params) {
            //console.log(vm.observadoresCallbacks[name], name + " notificado.")
            if (vm.observadoresCallbacks[name] != null) {
                angular.forEach(vm.observadoresCallbacks[name], function (callback) {
                    callback(params);
                });
            }
        };

        vm.addGeoServerLayer = function (escopo, name, ds) {

        };

        vm.vetorizarPontoImovel = function(pontoImomvel){

        }


        vm.habilitarBotoes = function (aba) {

        };

        /**
         * Permite adicicionar identificadores as interações
         */
        vm.loadPrototype = function () {
            /** Adiciona identificação as iterações **/
            ol.interaction.Draw.prototype.setId = function (id) {
                this.id = id;
            };
            ol.interaction.Draw.prototype.getId = function () {
                return this.id;
            };
            ol.Collection.prototype.findById = function (id) {
                var interacoes = this.getArray();
                for (var i = 0; i < interacoes.length; i++) {
                    var interacao = interacoes[i];
                    if (interacao && typeof interacao.getId == 'function' && interacao.getId() == id) {
                        return interacao;
                    }
                }
                return false;
            };
        },

        vm.getPai = function(e){
            var pai = $(e.target).parent();
            if($(e.target).prop("tagName") == 'I'){
                pai = pai.parent();
            }
            return pai;
        },

        vm.getHTMLControleCamadas = function(){
            return "<div class='font-class'>" +
                        "Camadas" +
                        "<br/>" +
                    "</div>" +
                    "<div layout='column' class='ng-scope layout-column'>" +
                        "<div layout='row' class='ng-scope layout-row> style='padding-left:10px'>" +
                            "<input type='checkbox' id='checkboxCamadaTerrenoCadastral' onclick='habilitarCamadaTerrenoCadastral()'>" +
                                "<label for='checkboxCamadaTerrenoCadastral'>  Terreno Cadastral</label>" +
                            "</input>" +
                        "</div>" +
                        "<br/>" +
                        "<div layout='row' class='ng-scope layout-row style='padding-left:10px'>" +
                            "<input type='checkbox' id='checkboxCamadaTerrenoCartorial' onclick='habilitarCamadaTerrenoCartorial()'>" +
                                "<label for='checkboxCamadaTerrenoCartorial'>  Terreno Cartorial</label>" +
                            "</input>" +
                        "</div>" +
                        "<br/>" +
                        "<br/>"  +
                        "<div layout='row' class='ng-scope layout-row style='padding-left:10px'>" +
                            "<input type='checkbox' id='checkboxCamadaBenfeitorias' onclick='hebilitarCamadaBenfeitorias()'>" +
                                "   Benfeitorias" +
                            "</input>" +
                        "</div>" +
                    "</div>"
        }


        vm.criaControleCamadas = function(params){
            return function (opt_options) {

                var options = opt_options || {};

                var anchor = document.createElement('a');
                anchor.removeAttribute('href');
                anchor.setAttribute('title', params.title);
                anchor.style.cursor = 'pointer';
                /*anchor.innerHTML = vm.getHTMLControleCamadas();*/

                var element = document.createElement('div');
                element.appendChild(anchor);
                element.setAttribute('id', params.id);

                ol.control.Control.call(this, {
                    element:element,
                    map: options.map,
                    target: options.target
                });
            };
        };

        vm.criarControleVizualizarSatelite = function(argFunc){
            return function(opt_options){
                var options = opt_options || {};

                var img = document.createElement('img');
                img.setAttribute('src', 'assets/img/Mapa-estatico.png');
                img.className = 'img-satelite';
                img.style.cursor = "pointer";

                img.addEventListener('click', function(e){
                    var elementoPai = vm.getPai(e);
                    argFunc(e);
                }, false);

                var element = document.createElement('div');
                element.className = 'ol-attribution-visualizar-satelite';
                element.appendChild(img);

                ol.control.Control.call(this, {
                    element: element,
                    map: options.map,
                    target: options.target
                });
            };
        };



        vm.test = function(argFunc){
            return function(options){

                var myDiv = angular.element(document.querySelector('#meuId'));
                ol.control.Control.call(this, {
                    element: myDiv,
                    map: options.map,
                    target: options.target
                });
            };
        };

        vm.criarControleExtendido = function (params, argFunc) {
            return function (opt_options) {

                var options = opt_options || {};

                // this.extent_ = this_.options.extent;

                var anchor = document.createElement('a');
                anchor.removeAttribute('href');
                anchor.setAttribute('alt', params.title);
                anchor.setAttribute('title', params.title);
                anchor.style.cursor = "pointer";

                var icon = document.createElement('i');
                icon.className = 'material-icons';
                icon.innerHTML = params.icone;

                anchor.appendChild(icon);
                anchor.addEventListener('click', function(e){
                    var elementoPai = vm.getPai(e);
                    if(elementoPai.hasClass('disabled')){
                        return;
                    }
                    argFunc(e);
                }, false);
                anchor.addEventListener('touchstart', argFunc, false);

                var element = document.createElement('div');
                element.className = 'control-extent ol-unselectable custom-control-extend';
                element.appendChild(anchor);
                element.setAttribute('id', params.id);

                ol.control.Control.call(this, {
                    element: element,
                    map: options.map,
                    target: options.target
                });

            };
        };

        vm.setTipoUnidadeAutonoma = function(tipoUnidadeAutonoma){
            vm.tipoUnidadeAutonoma = tipoUnidadeAutonoma;
        };
    }
})();

(function(){

  'use strict';

  angular.module('su-cadastro-imovel').controller('ArquivoController',
    ["$scope", "$http", "$mdDialog", "arquivo", "verificaObrigatorios", "ArquivoContratoUpload", "MensagensService", function ($scope, $http, $mdDialog, arquivo, verificaObrigatorios, ArquivoContratoUpload, MensagensService) {



      $scope.arquivo = arquivo;
      $scope.errorsForms=[];
      $scope.submitted=false;
      var self=this;
      self.hoje = new Date();
      $scope.fechar = function () {
        $mdDialog.cancel();
      };

      $scope.isCamposValidos = function(){
        $scope.errorsForms = verificaObrigatorios.getValoresByForm($scope.uploadArquivo,'imovel');
        if($scope.uploadArquivo.arquivoArq.$error && $scope.uploadArquivo.arquivoArq.$error.maxSize){
          $scope.errorsForms.push('O arquivo da foto não pode ser superior a 5MB.');
        }
        if($scope.errorsForms.length == 0){
          return true;
        }
        return false;
      };

      $scope.incluir = function(){
        $scope.submitted = true;

        if(validarExtensaoArquivo($scope.arquivo) & $scope.isCamposValidos()){
          salvarArquivo();
        }
      }

      var salvarArquivo = function() {
        ArquivoContratoUpload.save($scope.arquivo, function(response) {
          $scope.arquivo = response;

        }, function(error) {
          MensagensService.exibirMensagemErro(error.data.message);
        })
      };


      var validarExtensaoArquivo = function(file) {
        if(file == undefined)
          return false;
        else if (getExtensao(file.arquivo.name) == ".pdf")
          return true;
        else if (getExtensao(file.arquivo.name) == ".doc")
          return true;
        else if (getExtensao(file.arquivo.name) == ".docx")
          return true;
        else {
            MensagensService.exibirMensagemErro("Formato de arquivo inválido. São permitidos apenas arquivos do tipo .PDF, .DOC ou .DOCX");
          return false;
        }
      }

      var getExtensao = function(nomeArquivo){
        if(nomeArquivo && nomeArquivo.endsWith(".docx")){
          return nomeArquivo.substring(nomeArquivo.length - 5);
        }
        return nomeArquivo.substring(nomeArquivo.length - 4);
      }


      $scope.removerArquivo = function(){
        if(confirm("Tem certeza que deseja excluir este arquivo?")){
          $scope.arquivo = undefined;
          MensagensService.exibirMensagemSucesso("Certidão excluída com sucesso.");
        }
      }

      $scope.confirmar = function () {
        $mdDialog.hide($scope.arquivo);
      }

    }]);
})();


(function () {
  'use strict';
  pesquisarImovelController.$inject = ["$scope", "UfsService", "Dominio", "CidadesExtService", "PesquisaImovel", "PaisesUtilizadosService", "MunicipioService", "DetalharImovel", "$mdDialog", "TipoCancelImovelPesquisa", "Cancelamento", "$rootScope", "NotificadorService", "VerificaDestinacao", "VerificaUtilizacao", "MAPA_CONSTANTS_INCORPORACAO", "ciMapaViewFactory", "ciMapaLayersFactory", "$state", "PaginacaoFactory", "MensagensService", "$filter", "escopoCompartilhadoService", "camposAjuda", "SincronizarService"];
  angular
    .module('su-cadastro-imovel')
    .controller('PesquisarImovelController', pesquisarImovelController);

  function pesquisarImovelController(
    $scope,
    UfsService,
    Dominio,
    CidadesExtService,
    PesquisaImovel,
    PaisesUtilizadosService,
    MunicipioService,
    DetalharImovel,
    $mdDialog,
    TipoCancelImovelPesquisa,
    Cancelamento,
    $rootScope,
    NotificadorService,
    VerificaDestinacao,
    VerificaUtilizacao,
    MAPA_CONSTANTS_INCORPORACAO,
    ciMapaViewFactory,
    ciMapaLayersFactory,
    $state,
    PaginacaoFactory,
    MensagensService,
    $filter,
    escopoCompartilhadoService,
    camposAjuda,
    SincronizarService
  ) {
    var vm = this,
      notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES;
    vm.ufs = UfsService.UFS;
    vm.paises = PaisesUtilizadosService.query();
    vm.filtroImovel = {};
    vm.resultadoPesquisa = [];
    vm.cidades = [];
    vm.municipios = [];
    vm.ordenarPor = '';
    vm.modoExibicao = {};
    vm.exibirResultadoMapa = false;
    vm.paginacao = PaginacaoFactory.getDefault();
    ciMapaViewFactory.init();
    ciMapaLayersFactory.init(false);

    vm.modoExibicaoOpt = [
      {
        id: 1,
        descricao: 'Lista',
        codigoSistemico: 'INC_FORM_PESQ_IMOVEL_LISTA'
      },
      {
        id: 2,
        descricao: 'Mapa',
        codigoSistemico: 'INC_FORM_PESQ_IMOVEL_MAPA'
      }
    ];

    vm.ordenarPorOpt = [
      {
        id: 1,
        descricao: 'UF',
        valor: 'enderecamento.uf',
        codigoSistemico: 'INC_FORM_PESQ_IMOVEL_ORDEN_POR_UF'
      },
      {
        id: 2,
        descricao: 'Município',
        valor: 'enderecamento.municipio',
        codigoSistemico: 'INC_FORM_PESQ_IMOVEL_ORDEN_POR_MUN'
      },
      {
        id: 3,
        descricao: 'Data de cadastramento',
        valor: 'dataCadastro',
        codigoSistemico: 'INC_FORM_PESQ_IMOVEL_ORDEN_POR_DT_CAD'
      }
    ];

    escopoCompartilhadoService.setObjetos('hints', camposAjuda);

    vm.pesquisar = function () {
      var dataInicio = vm.filtroImovel.periodoCadastramentoInicio
          ? $filter('date')(
              vm.filtroImovel.periodoCadastramentoInicio,
              'MM/dd/yyyy'
            )
          : null,
        dataFim = vm.filtroImovel.periodoCadastramentoFim
          ? $filter('date')(
              vm.filtroImovel.periodoCadastramentoFim,
              'MM/dd/yyyy'
            )
          : null;
      if (vm.modoExibicao.descricao === 'Mapa') {
        PesquisaImovel.query({
          rip: vm.filtroImovel.rip,
          idPais: vm.filtroImovel.enderecamento.pais.id,
          dataInicio: dataInicio,
          dataFim: dataFim,
          uf: vm.filtroImovel.enderecamento.uf,
          municipio: vm.filtroImovel.enderecamento.municipio,
          bairro: vm.filtroImovel.enderecamento.bairro,
          cidadeExterior: vm.filtroImovel.enderecamento.cidadeExterior,
          numeroProcesso: vm.filtroImovel.numeroProcesso,
          matricula: vm.filtroImovel.matricula,
          cpfUsuario: vm.filtroImovel.cpfUsuario,
          cpfCnpjProprietarios: vm.filtroImovel.cpfCnpjProprietarios
        }).$promise.then(function (data) {
          vm.resultadoPesquisa = data.content;
          NotificadorService.notify(
            $scope,
            vm.resultadoPesquisa,
            notifiers.RESULTADOPESQUISARIMOVEL
          );
        });
        vm.exibirResultadoMapa = true;
      } else {
        vm.exibirResultadoMapa = false;
        vm.paginacao.page = 1;
        PesquisaImovel.query({
          rip: vm.filtroImovel.rip,
          idPais: vm.filtroImovel.enderecamento.pais.id,
          dataInicio: dataInicio,
          dataFim: dataFim,
          uf: vm.filtroImovel.enderecamento.uf,
          municipio: vm.filtroImovel.enderecamento.municipio,
          bairro: vm.filtroImovel.enderecamento.bairro,
          cidadeExterior: vm.filtroImovel.enderecamento.cidadeExterior,
          numeroProcesso: vm.filtroImovel.numeroProcesso,
          matricula: vm.filtroImovel.matricula,
          cpfUsuario: vm.filtroImovel.cpfUsuario,
          cpfCnpjProprietarios: vm.filtroImovel.cpfCnpjProprietarios,
          numeroPagina: vm.paginacao.page,
          maximoPorPagina: vm.paginacao.limit,
          campoOrdenacao: vm.ordenarPor
        }).$promise.then(function (data) {
          if (data.content.length > 0) {
            vm.resultadoPesquisa = data.content;
            vm.paginacao.totalElements = data.totalElements;
          } else {
            MensagensService.exibirMensagemErro('Nenhum imóvel encontrado');
            vm.resultadoPesquisa = [];
          }
        });
      }
    };

    vm.page = function () {
      var filtroSerializado = angular.toJson(vm.filtroImovel);
      PesquisaImovel.query({
        imovelPesquisa: filtroSerializado,
        numeroPagina: vm.paginacao.page,
        maximoPorPagina: vm.paginacao.limit,
        campoOrdenacao: vm.ordenarPor
      }).$promise.then(function (data) {
        vm.resultadoPesquisa = data.content;
      });
    };

    vm.limpar = function () {
      vm.filtroImovel = {};
      vm.filtroImovel.enderecamento = {};
      vm.filtroImovel.enderecamento.pais = Dominio.Constantes.Brasil;
    };

    vm.buscarCidadesPorPais = function () {
      vm.filtroImovel.enderecamento.cidadeExterior = null;
      if (vm.filtroImovel.enderecamento.pais.id == 1) {
        vm.cidades = [];
      } else {
        vm.cidades = CidadesExtService.query({
          id_pais: vm.filtroImovel.enderecamento.pais.id
        });
      }
    };

    vm.buscarMunicipiosPorUf = function () {
      if (vm.filtroImovel.enderecamento.uf) {
        vm.filtroImovel.enderecamento.municipio = '';
        vm.municipios = MunicipioService.TodosMunicipios.query({
          uf: vm.filtroImovel.enderecamento.uf.sigla
        });
      }
    };

    vm.buscarImovelMapa = function (imovel) {
      return DetalharImovel.get({ id: imovel.id }).$promise.then(function (
        data
      ) {
        return data.imovelOriginal;
      });
    };

    var localizacaoDialogController = function ($scope, $mdDialog, imovel) {
      $scope.imovel = imovel;

      $scope.fechar = function () {
        $mdDialog.cancel();
      };
    };
    localizacaoDialogController.$inject = ["$scope", "$mdDialog", "imovel"];

    vm.showLocalizacaoImovelDialog = function (ev, imovel) {
      $mdDialog.show({
        controller: localizacaoDialogController,
        templateUrl:
          'scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-localizacao-imovel.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: true,
        locals: {
          imovel: imovel
        }
      });
    };

    var dialogCancelarImovelController = function (
      $scope,
      $mdDialog,
      imovel,
      motivos,
      cadastrador,
      Cancelamento
    ) {
      $scope.motivos = motivos;
      $scope.hoje = new Date();
      $scope.nomeCadastrador = cadastrador;
      $scope.cancelamentoImovel = {};
      $scope.cancelamentoImovel.usuarioCadastrador = 1;
      $scope.cancelamentoImovel.imovel = imovel;

      $scope.fechar = function () {
        $mdDialog.cancel();
      };

      $scope.salvarCancelamento = function (invalidForm) {
        $scope.submittedCancelamento = true;
        eval.errorsForms = [];
        var cancelamentoSerializado = angular.toJson($scope.cancelamentoImovel);
        if (!invalidForm) {
          var dialog = angular.copy($mdDialog);
          var confirm = dialog
            .confirm({
              focusOnOpen: false,
              skipHide: true,
              controller: function () {
                this.abort = function () {
                  dialog.hide();
                };
                this.hide = function () {
                  dialog.hide();
                  $mdDialog.hide();
                  Cancelamento.save(cancelamentoSerializado, function (data) {
                    MensagensService.exibirMensagemSucesso(
                      'Cancelamento realizado com sucesso'
                    );
                    vm.pesquisar();
                  });
                };
              }
            })
            .content('O RIP será CANCELADO. Deseja continuar?')
            .ariaLabel('Cancelar Imóvel')
            .cancel('Não')
            .ok('Sim');
          dialog.show(confirm);
        } else {
          if ($scope.cancelamentoImovel.tipoCancelamentoImovel == undefined) {
            eval.errorsForms.push('Motivo');
          } else if (
            $scope.cancelamentoImovel.tipoCancelamentoImovel.id == 3 &&
            $scope.cancelamentoImovel.justificativa === undefined &&
            $scope.submittedCancelamento
          ) {
            eval.errorsForms.push('Justificativa');
          } else if (
            $scope.cancelamentoImovel.tipoCancelamentoImovel.id == 4 &&
            !$scope.cancelamentoImovel.numerProcesso
          ) {
            eval.errorsForms.push('Número de Processo');
          }
          MensagensService.exibirMensagemCamposObrigatorios();
        }
      };
    };
    dialogCancelarImovelController.$inject = ["$scope", "$mdDialog", "imovel", "motivos", "cadastrador", "Cancelamento"];

    vm.showCancelarImovel = function (ev, imovel, index) {
      if (imovel.rip) {
        VerificaUtilizacao.test(imovel).then(function success(result) {
          if (result.status != 200 && result.status != 404) {
            //Se o imóvel não foi sincronizado, o serviço retorna 404 e deve seguir com o cancelamento
            MensagensService.exibirMensagemErro(
              'Não foi possível conectar com o serviço de destinação.'
            );
          } else {
            if (!result.data) {
              $mdDialog.show({
                controller: dialogCancelarImovelController,
                templateUrl:
                  'scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-cancelar-imovel.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose: true,
                locals: {
                  imovel: imovel,
                  motivos: TipoCancelImovelPesquisa.query(),
                  cadastrador:
                    $rootScope.usuarioLogado.nome +
                    ' ' +
                    $rootScope.usuarioLogado.cpf,
                  Cancelamento: Cancelamento
                }
              });
            } else {
              MensagensService.exibirMensagemErro(
                'Este imóvel não pode ser cancelado pois há destinações ativas vinculadas a ele.'
              );
            }
          }
        });
      } else {
        var cancelamentoSerializado = {};
        cancelamentoSerializado.imovel = imovel;
        var dialog = angular.copy($mdDialog);
        var confirm = dialog
          .confirm({
            focusOnOpen: false,
            skipHide: true,
            controller: function () {
              this.abort = function () {
                dialog.hide();
              };
              this.hide = function () {
                dialog.hide();
                $mdDialog.hide();
                Cancelamento.save(cancelamentoSerializado, function (data) {
                  MensagensService.exibirMensagemSucesso(
                    'Cancelamento realizado com sucesso'
                  );
                  vm.pesquisar();
                });
              };
            }
          })
          .content('O RIP será CANCELADO. Deseja continuar?')
          .ariaLabel('Cancelar Imóvel')
          .cancel('Não')
          .ok('Sim');
        dialog.show(confirm);
      }
    };

    vm.exportarPesquisa = function (tipo) {
      var fileName = 'imoveis.' + tipo;
      var a = document.createElement('a');
      document.body.appendChild(a);
      a.style = 'display: none';
      PesquisaImovel.downloadFile({
        rip: vm.filtroImovel.rip,
        pais: vm.filtroImovel.enderecamento.pais.id,
        dataInicio: vm.filtroImovel.periodoCadastramentoInicio,
        dataFim: vm.filtroImovel.periodoCadastramentoFim,
        uf: vm.filtroImovel.enderecamento.uf,
        municipio: vm.filtroImovel.enderecamento.municipio,
        bairro: vm.filtroImovel.enderecamento.bairro,
        cidadeExterior: vm.filtroImovel.enderecamento.cidadeExterior,
        tipo: tipo
      }).$promise.then(function (result) {
        var file = new Blob([result.data], { type: 'application/' + tipo });
        var fileURL = window.URL.createObjectURL(file);
        a.href = fileURL;
        a.download = fileName;
        a.click();
      });
    };

    vm.irParaEdicao = function (idImovel) {
      $state.go('INCORPORACAO.alterarCadastro', { id: idImovel, tab: -1 });
    };

    vm.irParaDetalhamento = function (idImovel) {
      $state.go('INCORPORACAO.detalharImovel', { id: idImovel });
    };

    function isRecusado(item) {
      return item.isRecusado;
    }

    function isAtivo(item) {
      return item.situacaoImovel.id === 1;
    }
    function isCancelado(item) {
      return item.situacaoImovel.id === 3;
    }

    function isCadastradorAutorizado(item) {
      return item.isCadastradorAutorizado;
    }

    function isSuspensoEdicaoCancelamento(item) {
      return item.isSuspensoEdicaoCancelamento;
    }

    function isSincSpiunet(item) {
      return !!item.dsSpiunet;
    }

    vm.exibeEditar = function (item) {
      return (
        (isRecusado(item.imovelAlterado) || isAtivo(item.imovelAlterado)) &&
        isCadastradorAutorizado(item.imovelAlterado) &&
        !isSuspensoEdicaoCancelamento(item)
      );
    };

    vm.exibeCancelar = function (item) {
      return (
        !isCancelado(item) && !isSuspensoEdicaoCancelamento(item)
        //&& !isSincSpiunet(item)
      );
    };
  }
})();

(function () {
  'use strict';
  angular
    .module('su-cadastro-imovel')
    .controller(
      'LocalizacaoController',
      ["$scope", "$timeout", "$window", "CidadesExtService", "RipsFusao", "$rootScope", "SincronizarService", function (
        $scope,
        $timeout,
        $window,
        CidadesExtService,
        RipsFusao,
        $rootScope,
        SincronizarService
      ) {
        var self = this;
        self.cidades = [];
        self.cidadeExterior = undefined;

        $scope.$watch('usuarioLogado', function (newValue, oldValue) {
          if ($rootScope.usuarioLogado) {
            self.nomeUsuarioLogado = $rootScope.usuarioLogado.nome;
            self.cpfUsuarioLogado = $rootScope.usuarioLogado.cpf;
            self.jurisdicoesUsuarioLogado = $rootScope.usuarioLogado.jurisdicoes
              .sort()
              .toString();
            self.codigoUGUsuarioLogado = $rootScope.usuarioLogado.idOrganizacao;
            self.ugUsuarioLogado = $rootScope.usuarioLogado.organizacao;
            self.hasPermissaoHomologar =
              $rootScope.usuarioLogado.permissoes.includes(
                'CAD_IMOVEL.HOMOLOGAR_IMOVEL'
              );
            self.hasPermissaoUniao =
              $rootScope.usuarioLogado.permissoes.includes(
                'CAD_IMOVEL.CADASTRAR_IMOVEL_UNIAO'
              );
            self.permissoesCadastro =
              self.ugUsuarioLogado + (self.hasPermissaoUniao ? ' e UNIÃO' : '');
          }
        });

        self.buscaCidadesPorPais = function () {
          var pais = $scope.imovel.enderecamento.pais.id;
          if (pais != 1 && pais != null) {
            self.cidades = CidadesExtService.query({ id_pais: pais });
          }
        };

        self.atualizaCidadeExterior = function () {
          if (self.cidadeExterior == 0) {
            $scope.imovel.enderecamento.cidadeExterior = null;
          } else {
            $scope.imovel.enderecamento.cidadeExterior = self.cidadeExterior;
          }
        };
      }]
    );
})();

(function () {
    'use strict';
    angular
        .module('su-cadastro-imovel')
        .controller('ImovelController', ImovelController);

    ImovelController.$inject = [
        '$scope',
        '$timeout',
        'Imovel',
        'Dominio',
        'MunicipioService',
        'CartorioService',
        'camposAjuda',
        'escopoCompartilhadoService',
        'tabsIncorporacao',
        '$mdDialog',
        'TipoAquisicaoService',
        'ExibirCertidao',
        'ExibirPlantaUnidade',
        'verificaObrigatorios',
        '$state',
        '$rootScope',
        'ImovelModel',
        'NotificadorService',
        'NOTIFICADORES',
        'Documento',
        '$filter',
        'ciMapaViewFactory',
        'MAPA_CONSTANTS_INCORPORACAO',
        'ciMapaLayersFactory',
        'ValidaRipFracionamentoService',
        'imovelService',
        'ValidaRipFusaoService',
        'UnidadeAutonomaService',
        'MensagensService',
        'PaginacaoFactory',
        'TerrenoService',
        'TipoImovelFactory',
        'ArquivoService',
        'homologarEnviarDestinacaoService',
        'ABA_LOCALIZACAO',
        'ABA_DADOS_GERAIS',
        'ABA_TERRENO',
        'ABA_PROPRIETARIO',
        'CNPJ_UNIAO',
        'TIPO_PROPRIETARIO_UNIAO',
        'ABA_UNIDADE_AUTONOMA'
    ];

    function ImovelController(
        $scope,
        $timeout,
        Imovel,
        Dominio,
        MunicipioService,
        CartorioService,
        camposAjuda,
        escopoCompartilhadoService,
        tabsIncorporacao,
        $mdDialog,
        TipoAquisicaoService,
        ExibirCertidao,
        ExibirPlantaUnidade,
        verificaObrigatorios,
        $state,
        $rootScope,
        ImovelModel,
        NotificadorService,
        NOTIFICADORES,
        Documento,
        $filter,
        ciMapaViewFactory,
        MAPA_CONSTANTS_INCORPORACAO,
        ciMapaLayersFactory,
        ValidaRipFracionamentoService,
        imovelService,
        ValidaRipFusaoService,
        UnidadeAutonomaService,
        MensagensService,
        PaginacaoFactory,
        TerrenoService,
        TipoImovelFactory,
        ArquivoService,
        homologarEnviarDestinacaoService,
        ABA_LOCALIZACAO,
        ABA_DADOS_GERAIS,
        ABA_TERRENO,
        ABA_PROPRIETARIO,
        CNPJ_UNIAO,
        TIPO_PROPRIETARIO_UNIAO,
        ABA_UNIDADE_AUTONOMA
    ) {

        var self = this,
            imagensBenfeitoria = [],
            contIdSerializacao = 99,
            notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES,
            featureIds = MAPA_CONSTANTS_INCORPORACAO.FEATURE_ID;

        self.tabs = tabsIncorporacao;
        self.valorTipoProprietarioReal = undefined;
        $scope.forms = {};
        $scope.selecionaUF = '';
        $scope.selecionaMunicipio = '';
        $scope.dadosPonto = ImovelModel.dadosPonto;
        self.outrosDocumentos = [];
        self.documentos = [];
        self.ufs = [];
        self.municipios = [];

        self.isInclusaoBenfeitoria = false;
        self.mostraTabTituloAquisitivo = false;
        self.mostraTabFormDocumento = false;
        self.hoje = new Date();
        self.tiposAquisicao = TipoAquisicaoService.query();
        Dominio.tiposProprietarioParcial().then(function (result) {
            self.tiposProprietarioParcial = result;
        });

        self.isGaragem = false;
        self.isUnidadeAutonoma = false;
        self.modelTipoImovel = undefined;
        self.tiposDesenhoGeometriaTerreno = {
            tipoGeometriaCadastral: undefined,
            tipoGeometriaCartorial: undefined,
        };
        self.terceiroProprietario = {};
        self.terceiroProprietarioAnterior = {};
        self.tipoCadastroEdificacaoUnidadeAutonoma = undefined;
        self.edificacoesUnidadeAutonoma = [];
        self.isPontoInUnidadeAutonoma = undefined;
        self.paginacaoBenfeitorias = PaginacaoFactory.getDefault();
        self.geometriaUnidadeAutonomaValida = false;
        self.geometriaTerrenoCadastralValida = false;
        self.enderecamentoEspelho = {};
        escopoCompartilhadoService.setObjetos('hints', camposAjuda);

        $scope.$on('exibirMapaSelecao', function (event, args) {
            self.tipoCadastroEdificacaoUnidadeAutonoma = args.tipo;
            if (args.tipo === 'EXISTENTE') {
                self.showFormularioUnidadeAutonoma = function () {
                    return false;
                };
            }
        });

        var TAMANHO_RIP_VALIDO = 8;

        // Tipos proprietário parcial
        var ID_AUTARQUIAFUNDACAO_PARCIAL = 2;

        // Tipos proprietário real
        var ID_UNIAO_PROPRIETARIO_REAL = 1,
            ID_AUTARQUIAFUNDACAO_REAL = 5,
            ID_TERCEIROS_PROPRIETARIO_REAL = 6;

        // Formas de incorporação
        var ID_FORMA_INC_ORIGINALMENTE_DA_UNIAO = 1,
            ID_POR_AQUISICAO = 2,
            ID_POR_FRACIONAMENTO = 3,
            ID_POR_FUSAO = 4,
            ID_IMOVEL_DE_TERCEIROS = 5;

        //Tipos de aquisicao
        var ID_TIPO_AQUISICAO_HERANCA_JACENTE = 6,
            ID_TIPO_AQUISICAO_SUCESSAO_POR_ENTIDADES_APF = 9,
            ID_TIPO_AQUISICAO_ARREMATACAO_JUDICIAL = 10,
            ID_TIPO_AQUISICAO_REGISTRO_POR_APOSSAMENTO = 11,
            ID_TIPO_AQUISICAO_DESAPROPRIACAO_JUDICIAL = 16;

        //Tipos de Situação de incorporação
        var ID_SITUACAO_INCORPORACAO_INCORPORADO = 1;
        var ID_SITUACAO_INCORPORACAO_EMPROCESSO = 2;

        // Tipos Imovel
        var ID_TIPO_IMOVEL_ESPELHO_DAGUA = 3;

        self.tabs.removeTabHistorico();

        // Início WATCHES

        $scope.$watch(
            'usuarioLogado',
            /**
             * @property {integer} $rootScope.usuarioLogado.idOrganizacao - Identificador da organização do usuário logado.
             * @property {string} $rootScope.usuarioLogado.organizacao - Nome da Organização do usuário logado.
             */
            function () {
                if ($rootScope.usuarioLogado) {
                    self.usuarioLog = $rootScope.usuarioLogado;
                    self.codigoUGUsuarioLogado = $rootScope.usuarioLogado.idOrganizacao;
                    self.ugUsuarioLogado = $rootScope.usuarioLogado.organizacao;
                    self.jurisdicoes = $rootScope.usuarioLogado.jurisdicoes;
                }
            }
        );

        $scope.$watch('imovel.tipoImovel', function (value) {
            if (value != undefined) {
                self.isGaragem = value.descricao.toUpperCase() == 'GARAGEM' ? true : false;
            }
            if (value != undefined) {
                if (value.descricao.toUpperCase() != 'Gleba/Terreno/Lote com edificação'.toUpperCase() &&
                    value.descricao.toUpperCase() != 'Gleba/Terreno/Lote sem edificação'.toUpperCase()) {
                    self.tipoCadastroEdificacaoUnidadeAutonoma = true;
                } else {
                    self.tipoCadastroEdificacaoUnidadeAutonoma = false;
                }
            }
        });

        $scope.$watch(
            function (scope) {
                return scope.imovel.incorporacao.tipoIncorporacao;
            },
            function () {
                if (self.imovel.incorporacao.tipoIncorporacao !== null && angular.isDefined(self.imovel.incorporacao.tipoIncorporacao)) {
                    var fn =
                        self.tiposIncorporacaoWatchStrategy[
                        self.imovel.incorporacao.tipoIncorporacao.id
                        ];
                    if (angular.isDefined(fn)) {
                        fn();
                    }
                }
            }
        );

        $scope.$watch(
            'imovelCtrl.imovel.terreno.benfeitoria',
            /**
             * @param {string} value - Novo valor para o atributo benfeitoria.
             */
            function () {
                self.atualizaTabs();
            }
        );

        var validarExtensaoArquivoDocumentacaoUA = function (arquivo) {
            if (getExtensaoDocumentacaoUA(arquivo.name) == '.pdf') return true;
            else if (getExtensaoDocumentacaoUA(arquivo.name) == '.png') return true;
            else if (
                getExtensaoDocumentacaoUA(arquivo.name) == '.jpg' ||
                getExtensaoDocumentacaoUA(arquivo.name) == '.jpeg'
            )
                return true;
            else {
                MensagensService.exibirMensagemErro(
                    'Formato de arquivo inválido. São permitidos apenas arquivos do tipo PDF.'
                );
                return false;
            }
        };

        $scope.$watch(
            'imovelCtrl.imovel.tipoImovel',
            /**
             * @param {object} value - Novo valor para o atributo tipoImovel.
             * @param {integer} [value.id] - Identificador.
             */
            function (value) {
                if (angular.isDefined(value)) {
                    if (
                        self.imovel.pontoImovel &&
                        self.imovel.tipoImovel.tipoPai !== 'UNIDADE AUTÔNOMA' &&
                        self.isPontoInUnidadeAutonoma
                    ) {
                        var confirm = $mdDialog.confirm({
                            focusOnOpen: false,
                            textContent: 'Se o tipo do imóvel for alterado o ponto será removido. Deseja confirmar?',
                            ok: 'Sim',
                            cancel: 'Não',
                        });

                        $mdDialog.show(confirm).then(
                            function () {
                                self.limpaDadosRecuperadosUnidadeAutonoma();
                            },
                            function () {
                                self.imovel.tipoImovel = undefined;
                            }
                        );
                    }
                }
            }
        );

        $scope.$watch(
            /**
             * @return {*}
             */
            function () {
                if (!self.benfeitoria.edificacao) {
                    self.benfeitoria.edificacao = {};
                }
                return self.benfeitoria.edificacao.semInformacaoAcessibilidade;
            },
            /**
             * @param {boolean} newValue - novo valor para o atributo semInformacaoAcessibilidade.
             * @param {boolean} oldValue - antigo valor do atributo semInformacaoAcessibilidade.
             */
            function (newValue, oldValue) {
                if (newValue !== oldValue) {
                    if (newValue) {
                        //self.benfeitoria.edificacao.vagasSinalizadas = undefined;
                        //self.benfeitoria.edificacao.acessoLivreObstaculos = undefined;
                        //self.benfeitoria.edificacao.banheiroAcessivel = undefined;
                        //self.benfeitoria.edificacao.elevadorAcessivel = undefined;
                        self.benfeitoria.edificacao.vagasDefFisicos = undefined;
                        self.benfeitoria.edificacao.vagasIdosos = undefined;
                        self.benfeitoria.edificacao.calcadasRebaixa = undefined;
                        self.benfeitoria.edificacao.calcadasLivres = undefined;
                        self.benfeitoria.edificacao.entradaLivre = undefined;
                        self.benfeitoria.edificacao.pavimentosLivreCadRodas = undefined;
                        self.benfeitoria.edificacao.balcaoInformacoes = undefined;
                        self.benfeitoria.edificacao.pisoTatil = undefined;
                        self.benfeitoria.edificacao.corredorLargo = undefined;
                        self.benfeitoria.edificacao.sanitarioPublico = undefined;
                        self.benfeitoria.edificacao.braile = undefined;
                        self.benfeitoria.edificacao.libras = undefined;
                        self.benfeitoria.edificacao.possuiLaudo = undefined;
                        self.benfeitoria.edificacao.linkLaudo = undefined;
                        self.benfeitoria.edificacao.possuiPlanoTrabalho = undefined;
                        self.benfeitoria.edificacao.linkPlanoTrabalho = undefined;
                    }
                }
            }
        );

        $scope.$watch(
            function () {
                return self.benfeitoria.tipoBenfeitoria;
            },
            function (newValue) {
                if (newValue) {
                    if (newValue.id === 2) {
                        self.benfeitoria.geometriaBenfeitoria = undefined;
                        self.tipoDesenhoBenfeitoria = undefined;
                        NotificadorService.notify(
                            $scope, {
                            codigo: self.benfeitoria.codigo,
                            geometriaBenfeitoria: self.benfeitoria.geometriaBenfeitoria,
                        },
                            notifiers.DESFAZER_ALTERACAO_BENFEITORIA
                        );
                    }
                }
            }
        );

        // Fim WATCHES

        // Início NOTIFICADORES

        NotificadorService.subscribe(
            $scope,
            function (evento, escopo, isPontoInsercao) {
                self.pontoNaInsercao = isPontoInsercao;
            },
            notifiers.VERIFICAR_PONTO_NA_ARESTA
        );

        NotificadorService.subscribe(
            $scope,
            function (evento, escopo, id) {
                if (id === featureIds.PONTO_IMOVEL) {
                    $scope.dadosPonto = undefined;
                    $scope.imovel.pontoImovel = undefined;
                } else if (id === featureIds.UNIDADE_AUTONOMA) {
                    $scope.imovel.unidadeAutonoma.geometriaUnidadeAutonoma = undefined;
                }
            },
            notifiers.FEATURE_EXCLUIDA
        );

        NotificadorService.subscribe(
            $scope,
            function () {
                delete self.imovel.terreno.arquivoCadastral;
                delete self.imovel.terreno.geometriaTerreno;
            },
            NOTIFICADORES.DELETESHAPEFILECADASTRAL
        );

        NotificadorService.subscribe(
            $scope,
            function () {
                delete self.imovel.terreno.arquivoCartorial;
                delete self.imovel.terreno.geometriaTerrenoCartorial;
            },
            notifiers.DELETESHAPEFILECARTORIAL
        );

        NotificadorService.subscribe(
            $scope,
            function (evento, escopo, tipoImovel) {
                self.modelTipoImovel = tipoImovel;
            },
            notifiers.LIMPAR_TIPO_IMOVEL
        );

        NotificadorService.subscribe(
            $scope,
            function (evento, escopo, isPontoInUnidadeAutonoma) {
                self.isPontoInUnidadeAutonoma = isPontoInUnidadeAutonoma;
            },
            notifiers.PONTO_IN_UNIDADE_AUTONOMA
        );

        NotificadorService.subscribe(
            $scope,
            function (evento, escopo, data) {
                self.imovel.unidadeAutonoma.edificacao.geometria = data.geometria;
                self.geometriaUnidadeAutonomaValida = data.isValid;
            },
            notifiers.GEOMETRIA_UNIDADE_AUTONOMA_ALTERADA
        );

        NotificadorService.subscribe(
            $scope,
            function (evento, escopo, data) {
                self.imovel.terreno.geometriaTerreno = data.geometria;
                self.geometriaTerrenoCadastralValida = data.isValid;
                self.pontoNaInsercao = false;
            },
            notifiers.GEOMETRIA_TERRENO_CADASTRAL_ALTERADA
        );

        NotificadorService.subscribe($scope, function (evento, escopo, objeto) {
            self.documento = objeto;
        }, NOTIFICADORES.EDITAR_DOCUMENTO_UA);

        //   NotificadorService.subscribe(
        //     $scope,
        //     function(evento, escopo, data) {
        //         $scope.ufEspelhoDagua = data.uf;
        //         $scope.municipioEspelhoDagua = data.municipio;
        //         console.log('entrou noficador uf-municipio');
        //         console.log($scope.ufEspelhoDagua);
        //         console.log($scope.municipioEspelhoDagua);
        //     },
        //     notifiers.ENDERECAMENTO_UF_MUNICIPIO
        // );

        // Fim NOTIFICADORES




        $scope.$on('setaTrueGeometriaValida', function (event, args) {
            $rootScope.$broadcast('carregaEdificacoes');
            self.geometriaTerrenoCadastralValida = true;
        });


        $scope.destacaBenfeitoriaMapa = function (codigo) {
            NotificadorService.notify($scope, codigo, notifiers.DESTACAR_BENFEITORIA);
        };

        self.inicializaImovel = function () {
            self.imovel = imovelService.novoImovel();
            self.imovel.enderecamento.pais = Dominio.Constantes.Brasil;
        };

        self.inicializaImovel();

        self.isPaisBrasil = function () {
            return self.imovel.enderecamento.pais.id === 1;
        };

        self.hasMatricula = function () {
            return self.imovel.incorporacao.matriculas.length > 0;
        };

        self.tabs.atualizaTabs(self.imovel.tipoImovel);

        $scope.imovel = self.imovel;
        $scope.imovelCriado = self.imovel;

        $scope.tabela = {
            limit: 5,
            limitsPage: [5, 10, 15],
            page: 1,
        };

        self.isImovelSincronizado = function () {
            if ($scope.imovel.dsSpiunet) {
                return true
            }
            return false
        }

        self.limparImovel = function (tabIndex) {
            /* var terreno = $scope.imovel.terreno;
             var benfeitorias = $scope.imovel.benfeitorias;
             var unidadeAutonoma = $scope.imovel.unidadeAutonoma;
             if (self.modelTipoImovel !== undefined) {
                 $scope.tentativa = true;
                 self.modelTipoImovel = false;
             }
             self.inicializaImovel();
             $scope.imovel = angular.copy(self.imovel);
             $scope.imovel.terreno = terreno;
             $scope.imovel.benfeitorias = benfeitorias;
             $scope.imovel.unidadeAutonoma = unidadeAutonoma;*/

            if (tabIndex == ABA_LOCALIZACAO) {
                if (self.modelTipoImovel !== undefined) {
                    $scope.tentativa = true;
                    self.modelTipoImovel = false;
                }

                $scope.imovel.tipoImovel = undefined;
                $scope.imovel.enderecamento = [];
                $scope.imovel.numero = null;
                $scope.imovel.complemento = null;
                $scope.imovel.enderecamento.pais = Dominio.Constantes.Brasil;
                $scope.imovel.sistemaReferencia = { codigo: 4674, descricao: "SIRGAS 2000" };
                $scope.imovel.formatoCoordenadas = null;

                $scope.imovel.terreno = { arquivoCadastral: {} };

            } else if (tabIndex == ABA_DADOS_GERAIS) {
                $scope.imovel.tipoProcessoSei = null;
                $scope.imovel.numeroProcesso = null;
                $scope.imovel.naturezaImovel = null;
                $scope.imovel.numeroInscricaoMunicipal = null;
                $scope.imovel.numeroInscricaoRural = null;
                $scope.imovel.observacoesGerais = null;
                $scope.imovel.incorporacao = {
                    matriculas: [],
                    certidoesCartoriais: []
                };
                $scope.imovel.titulos = [];
                $scope.imovel.matriculas = [];
                $scope.imovel.ripsFusao = [];
                $scope.imovel.dsSpiunet = 'pend. sincronizacao';
                $scope.imovel.imagens = [];

            } else if (tabIndex == ABA_TERRENO) {
                if ($scope.imovel.terreno.id == null || $scope.imovel.terreno.id == undefined) {
                    $scope.imovel.terreno = { arquivoCadastral: {} };
                }
                $scope.imovel.arquivosComplementaresLista = [];

            } else if (tabIndex == ABA_PROPRIETARIO) {
                $scope.imovel.proprietarioTitular = {};
                $scope.imovel.proprietarioAnterior = {};
                $scope.imovel.proprietario = { pessoas: [] };
                $scope.imovel.benfeitoriaPertenceAoProprietario = undefined;
                //$scope.imovel.cnpjProprietario = null;
                $scope.imovel.proprietariosTerceiros = [];
                $scope.imovel.proprietariosParciais = [];
                $scope.imovel.proprietariosCartorio = [];
                $scope.imovel.registradoCartorio = false;
            }

            //remover pontos do mapa
            NotificadorService.notify($scope, tabIndex, notifiers.LIMPAR_ABA);
        };

        self.limparUnidadeAutonoma = function () {
            $scope.submittedUnidadeAutonoma = false;
            self.imovel.unidadeAutonoma = UnidadeAutonomaService.novaUnidadeAutonoma();
            self.imovel.fracaoIdealImovel = undefined;
            self.tipoCadastroEdificacaoUnidadeAutonoma = null;
            $scope.forms.adicionarUnidadeAutonomaForm.$setPristine();
            $scope.forms.adicionarUnidadeAutonomaForm.$setUntouched();
            //limpa desenho mapa
            NotificadorService.notify($scope, ABA_UNIDADE_AUTONOMA, notifiers.LIMPAR_ABA);
            //limpa arquivo shapefile
            NotificadorService.notify($scope, null, NOTIFICADORES.LIMPAR_ARQUIVO);
        };

        self.atualizaTabs = function () {
            if ($scope.imovel) {
                self.tabs.atualizaTabs($scope.imovel.tipoImovel);
            }
        };
        self.secaoAnterior = '';
        self.validaDadosLocalizacaoImovel = false;
        self.ultimaSecaoValidada = '';
        self.secoesExpandidas = [];

        self.validaSecao = function (ev, form, tab, secaoAtual) {
            var isSecaoAnteriorExpandida = false;
            var isSecaoAtualExpandida = false;
            var secaoAtualIndex = -1;
            var errorsForms = [];
            self.fecharAbaMapa = true;
            if (tab === Dominio.Constantes.tabLocalizacaoImovel) {
                self.validaDadosLocalizacaoImovel = !self.validaDadosLocalizacaoImovel;
                self.secaoAnterior = self.validaDadosLocalizacaoImovel ?
                    '.secaoDadosLocalizacaoImovel' :
                    '.secaoTipoImovel';
            } else if (tab === Dominio.Constantes.tabDadosGeraisImovel) {
                if (self.secaoAnterior === '') {
                    self.secaoAnterior = '.secaoDetalhesImovel';
                    self.secoesExpandidas.push('.secaoDetalhesImovel');
                }
            } else if (tab === Dominio.Constantes.tabUnidadeAutonoma) {
                if (self.secaoAnterior === '') {
                    self.secaoAnterior = '.secaoDadosEdificacao';
                    self.secoesExpandidas.push('.secaoDadosEdificacao');
                }
            } else if (tab === Dominio.Constantes.tabBenfeitoria) {
                if (self.secaoAnterior === '') {
                    self.secaoAnterior = '.secaoDadosAcessaoBenfeitoria';
                    self.secoesExpandidas.push('.secaoDadosAcessaoBenfeitoria');
                }
            }

            angular.forEach(self.secoesExpandidas, function (value, key) {
                if (self.secaoAnterior === value) {
                    isSecaoAnteriorExpandida = true;
                }

                if (secaoAtual === value) {
                    isSecaoAtualExpandida = true;
                    secaoAtualIndex = key;
                }
            });

            if (isSecaoAtualExpandida) {
                self.secoesExpandidas.splice(secaoAtualIndex, 1);
            } else {
                self.secoesExpandidas.push(secaoAtual);
            }

            if (
                (isSecaoAnteriorExpandida &&
                    self.secaoAnterior != self.ultimaSecaoValidada) ||
                tab === -1
            ) {
                popularErrorsFormsValidacaoSecao(self.secaoAnterior, form, errorsForms);
                self.ultimaSecaoValidada = self.secaoAnterior;
            } else if (
                isSecaoAtualExpandida &&
                secaoAtual !== self.ultimaSecaoValidada
            ) {
                popularErrorsFormsValidacaoSecao(secaoAtual, form, errorsForms);
                self.ultimaSecaoValidada = secaoAtual;
            } else {
                self.secaoAnterior = secaoAtual;
                return;
            }

            self.secaoAnterior = secaoAtual;
            if (errorsForms.length > 0) {
                MensagensService.exibirMensagemCamposObrigatorios();
            }
        };

        function popularErrorsFormsValidacaoSecao(secao, form, errorsForms) {
            var elementos = [];
            angular.forEach(angular.element(secao), function (value, key) {
                elementos.push(value.getAttribute('name'));
            });

            if (form && form.$error) {
                angular.forEach(form.$error.required, function (value, key) {
                    angular.forEach(elementos, function (valueElementos, keyElementos) {
                        if (valueElementos === value.$name) {
                            errorsForms.push(valueElementos);
                        }
                    });
                });
            }

            return errorsForms;
        }

        // INÍCIO CONTROLE DA ABA DE INCLUSÃO DE TÍTULO AQUISITIVO
        self.titulos = [];
        self.titulo = {
            tipoTituloAquisitivo: undefined,
            tipoAtoLegal: {},
            publicacao: {},
            moeda: {},
            encargos: [],
        };

        self.tituloBackup = {};

        //FIM CONTROLE DA ABA DE INCLUSÃO DE TÍTULO AQUISITIVO

        // INÍCIO CONTROLE DA ABA INCLUSÃO DE BENFEITORIA
        self.documentoBenfeitoria = {};
        self.benfeitoria = {
            tipoBenfeitoria: {},
            edificacao: {
                documentacao: [{
                    nome: 'Plano de Proteção Contra Incêndio',
                    possui: 'SEM_INFORMACAO',
                    isPrecadastrado: true,
                },
                {
                    nome: 'Habite-se',
                    possui: 'SEM_INFORMACAO',
                    isPrecadastrado: true,
                },
                {
                    nome: 'Alvará de Construção/Reforma',
                    possui: 'SEM_INFORMACAO',
                    isPrecadastrado: true,
                },
                {
                    nome: 'Laudo de Inspeção Predial',
                    possui: 'SEM_INFORMACAO',
                    isPrecadastrado: true,
                },
                {
                    nome: 'Relatório de Acessibilidade',
                    possui: 'SEM_INFORMACAO',
                    isPrecadastrado: true,
                },
                ],
                dadosMedidor: [],
            },
            imagens: [],
            plantas: [],
        };
        self.benfeitoriaBackup = {};

        self.benfeitorias = [];

        self.exibirTabInclusaoBenfeitoria = function (benfeitoria) {

            if (benfeitoria) {
                self.benfeitoriaBackup = angular.copy(benfeitoria);
                self.benfeitoria = angular.copy(benfeitoria);
            }
            self.isInclusaoBenfeitoria = true;
        };

        // FIM CONTROLE DA ABA INCLUSÃO DE BENFEITORIA

        // INÍCIO CONTROLE DA ABA DE DOCUMENTOS
        self.documento = {};
        self.documentoBackup = {};

        self.documentos = [];

        //FIM CONTROLE DA ABA DE DOCUMENTOS
        // CONTROLE DE BENFEITORIAS
        self.benfeitorias = [];

        self.benfeitoriaForm;

        self.editarBenfeitoria = function (obj) {
            self.benfeitoria = obj;
            imagensBenfeitoria.push(self.benfeitoria.imagens);
            $scope.showInserirBenfeitoriaDialog();
        };

        self.isCepGenerico = false;

        self.verificaCEPGenerico = function (stLocalidade) {
            return stLocalidade == 0;
        };

        self.cepValido = false;

        self.calcularAreaFracaoIdealUniao = function () {
            if (self.imovel.unidadeAutonoma) {
                var fracaoIdealCadastral =
                    self.imovel.unidadeAutonoma.fracaoIdealCadastral,
                    areaUniaoCadastral = self.imovel.terreno.areaUniaoCadastral,
                    areaTotalCadastral = self.imovel.terreno.areaTotalCadastral;

                var fracaoIdealCartorial =
                    self.imovel.unidadeAutonoma.fracaoIdealCartorial,
                    areaUniaoCartorial = self.imovel.terreno.areaUniaoCartorial,
                    areaTotalCartorial = self.imovel.terreno.areaTotalCartorial;

                if (fracaoIdealCadastral && areaUniaoCadastral && areaTotalCadastral) {
                    self.imovel.terreno.fracaoIdealUniaoCadastral =
                        fracaoIdealCadastral * (areaUniaoCadastral / areaTotalCadastral);
                }

                if (fracaoIdealCartorial && areaUniaoCartorial && areaTotalCartorial) {
                    self.imovel.terreno.fracaoIdealUniaoCartorial =
                        fracaoIdealCartorial * (areaUniaoCartorial / areaTotalCartorial);
                }
            }
        };

        self.salvarImovel = function () {

            limparIdSerializacaoPlantasBenfeitoria();

            self.calcularAreaFracaoIdealUniao();

            if (angular.equals({}, self.imovel.terreno.arquivoCartorial)) {
                self.imovel.terreno.arquivoCartorial = undefined;
            }

            if (angular.equals({}, self.imovel.terreno.arquivoCadastral)) {
                self.imovel.terreno.arquivoCadastral = undefined;
            }

            if (self.imovel.enderecamento.uf == undefined) {
                if (!self.isPaisBrasil) {
                    self.imovel.enderecamento.uf = 'EX';
                } else {
                    self.imovel.enderecamento.uf = self.imovel.enderecamento.ufImovel;
                    self.imovel.enderecamento.municipio = self.imovel.enderecamento.municipioImovel;
                }
            }

            var estaNasJurisdicoes = $rootScope.usuarioLogado.jurisdicoes.includes(
                self.imovel.enderecamento.uf
            );

            if (TipoImovelFactory.isEspelhoDagua(self.imovel.tipoImovel)) {
                estaNasJurisdicoes = true;
            }

            if (estaNasJurisdicoes) {
                if (self.imovel && self.imovel.unidadeAutonoma) {
                    self.imovel.unidadeAutonoma.documentos = self.documentos;
                }

                if (self.imovel.proprietario.tipoProprietarioReal.id === 5) {
                    self.imovel.proprietario.unidadeUsuario = self.ugUsuarioLogado;
                    self.imovel.proprietario.codigoUg = self.codigoUGUsuarioLogado;
                }

                if (self.imovel.incorporacao.cartorio != undefined) {
                    self.imovel.incorporacao.cartorioID =
                        self.imovel.incorporacao.cartorio.id;
                }

                if (self.imovel.proprietario.codigoOrgaoEntidade) {
                  if(typeof(self.imovel.proprietario.codigoOrgaoEntidade) == 'string'){
                    self.imovel.proprietario.codigoOrgaoEntidade = self.imovel.proprietario.codigoOrgaoEntidade.replace(/[^a-z0-9]/gi, "");
                  }
                }

                self.converterUnidadeDeMedida();
                self.confirmaSalvarImovel();
            } else {
                MensagensService.exibirMensagemErro(
                    'O imóvel deve estar contido nas jurisdições do usuário.'
                );
            }
        };
        var conversor = 10000;

        //independente da unidade de medida escolhida na tela, no banco salva em metro quadrado.
        self.converterUnidadeDeMedida = function () {
            if (self.imovel.terreno.unidadeMedidaCadastral == 'ha') {
                self.imovel.terreno.areaTotalCadastral =
                    self.imovel.terreno.areaTotalCadastral * conversor;
                self.imovel.terreno.areaUniaoCadastral =
                    self.imovel.terreno.areaUniaoCadastral * conversor;
                self.imovel.unidadeAutonoma.fracaoIdealCadastral =
                    self.imovel.unidadeAutonoma.fracaoIdealCadastral * conversor;
            }
            if (self.imovel.terreno.unidadeMedidaCartorial == 'ha') {
                self.imovel.terreno.areaTotalCartorial =
                    self.imovel.terreno.areaTotalCartorial * conversor;
                self.imovel.terreno.areaUniaoCartorial =
                    self.imovel.terreno.areaUniaoCartorial * conversor;
            }
        };

        function limparIdSerializacaoPlantasBenfeitoria() {
            var benfeitorias = self.imovel.benfeitorias;
            var plantas = null;
            if (benfeitorias != null && benfeitorias.length > 0) {
                for (var b = 0; b < benfeitorias.length; b++) {
                    if (benfeitorias[b].plantas.length > 0) {
                        plantas = benfeitorias[b].plantas;
                        for (var i = 0; i < plantas.length; i++) {
                            plantas[i]['@id'] = contIdSerializacao;
                            contIdSerializacao--;
                        }
                    }
                }
            }
        }
        self.tiposIncorporacaoWatchStrategy = {};

        self.tiposIncorporacaoWatchStrategy[
            Dominio.Constantes.OrigalmenteUniao
        ] = function () {
            self.imovel.incorporacao.direitoIncorporado =
                Dominio.Constantes.Propriedade;
        };

        self.PesquisaCartorioController = PesquisaCartorioController;

        self.PesquisaCartorioController.$inject = [
            '$scope',
            '$mdDialog',
            'ufSelecionada',
            'municipioSelecionado',
        ];

        $scope.pesquisaMunicipio = function (uf) {
            $scope.municipios = MunicipioService.TodosMunicipios.query({ uf: uf });
        };

        function PesquisaCartorioController(
            $scope,
            $mdDialog,
            ufSelecionada,
            municipioSelecionado
        ) {
            $scope.forms = {};

            $scope.ufSelecionada = ufSelecionada;
            $scope.municipioSelecionado = municipioSelecionado;

            $scope.hide = function () {
                $mdDialog.hide();
            };
            $scope.cancel = function () {
                $mdDialog.cancel();
            };

            $scope.selecionaCartorio = function (cartorio, cartorioForm, ev) {
                $scope.submittedCartorio = true;
                $scope.todosCamposPreenchidos = true;

                if (self.imovel.incorporacao == undefined) {
                    self.imovel.incorporacao = {};
                }

                if (!$scope.ufSelecionada ||
                    !$scope.municipioSelecionado ||
                    !$scope.cartorioSelecionado
                ) {
                    $scope.todosCamposPreenchidos = false;
                }

                if ($scope.todosCamposPreenchidos) {
                    self.imovel.incorporacao.cartorio = cartorio;
                    $mdDialog.hide();
                }
            };

            $scope.pesquisaMunicipio = function (uf) {
                $scope.municipios = MunicipioService.TodosMunicipios.query({ uf: uf });
            };
            $scope.$watch(
                function (scope) {
                    if (ufSelecionada != undefined) {
                        $scope.selecionaUF = 'uf.sigla != undefined';
                    }
                    return $scope.ufSelecionada;
                },
                function () {
                    if (
                        $scope.ufSelecionada != undefined &&
                        municipioSelecionado == undefined
                    ) {
                        $scope.cartorioSelecionado = undefined;
                        $scope.municipios = MunicipioService.TodosMunicipios.query({
                            uf: $scope.ufSelecionada,
                        });
                    }
                }
            );
            $scope.$watch(
                function (scope) {
                    if (municipioSelecionado != undefined) {
                        $scope.selecionaMunicipio =
                            'municipio.nome ===' + "'" + municipioSelecionado + "'";
                    }
                    return $scope.municipioSelecionado;
                },
                function () {
                    if ($scope.municipioSelecionado != undefined) {
                        MunicipioService.Municipio.query({
                            uf: ufSelecionada,
                            municipio: municipioSelecionado,
                        }).$promise.then(function (response) {
                            self.numeroMunicipio = response.codigo;
                            $scope.cartorioSelecionado = undefined;
                            $scope.cartorios = CartorioService.pesquisaCartoriosPorUF.query({
                                uf: $scope.ufSelecionada,
                                municipio: self.numeroMunicipio,
                            });
                        });
                    }
                }
            );
        }
        // controle de abas
        self.tabs.selectedIndex = -1;
        $scope.selectedIndex = self.tabs.selectedIndex;

        $scope.selectTab = function (num) {
            self.secaoAnterior = '';
            self.setSelectedIndex(num);
        };

        self.setSelectedIndex = function (num) {
            self.tabs.selectTab(num);

            $scope.selectedIndex = self.tabs.selectedIndex;
            $scope.$broadcast('mudouAba', { abaSelecionada: $scope.selectedIndex });
        };

        self.certidaoCartorial = {};
        self.imovel.certidoesCartorial = [];

        self.removerCertidao = function (index) {
            var confirm = $mdDialog
                .confirm()
                .textContent('Deseja realmente excluir este arquivo?')
                .ariaLabel('Excluir Arquivo')
                .targetEvent(index)
                .cancel('Não')
                .ok('Excluir');

            $mdDialog.show(confirm).then(
                function () {
                    self.imovel.certidoesCartorial.splice(index, 1);
                    MensagensService.exibirMensagemSucesso(
                        'Certidão excluída com sucesso.'
                    );
                    $mdDialog.hide();
                },
                function () {
                    $mdDialog.cancel();
                }
            );
        };

        self.resetarCertidaoCartorial = function () {
            self.certidaoCartorial = {};
        };

        self.showUploadCertidaoCartorialDialog = function (ev) {
            $scope.dialog = $mdDialog;
            $scope.dialog.show({
                controller: $scope.uploadCertidaoCartorialDialogController,
                templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-upload-certidao-cartorial.html',
                parent: angular.element(document.body),
                targetEvent: ev,

                clickOutsideToClose: false,
                locals: {
                    certidaoCartorial: self.certidaoCartorial,
                    certidoesCartorial: self.imovel.certidoesCartorial,
                    dataMatricula: self.imovel.incorporacao.dataMatricula,
                    validacaoController: $scope.validacaoController,
                },
            });
        };

        $scope.uploadCertidaoCartorialDialogController = uploadCertidaoCartorialDialogController;

        uploadCertidaoCartorialDialogController.$inject = [
            '$scope',
            '$http',
            '$mdDialog',
            'certidaoCartorial',
            'certidoesCartorial',
            '$sce',
            'CertidaoCartorial',
            'dataMatricula',
            'ExibirCertidao',
            'validacaoController',
            'verificaObrigatorios',
        ];

        function uploadCertidaoCartorialDialogController(
            $scope,
            $http,
            $mdDialog,
            certidaoCartorial,
            certidoesCartorial,
            $sce,
            CertidaoCartorial,
            dataMatricula,
            ExibirCertidao,
            validacaoController,
            verificaObrigatorios
        ) {
            $scope.certidaoCartorial = certidaoCartorial;
            $scope.certidoesCartorial = certidoesCartorial;

            $scope.errorsForms = [];

            $scope.fechar = function () {
                $mdDialog.cancel();
            };

            $scope.incluir = function () {
                $scope.certidaoCartorialSubmitted = true;
                var dataMatriculaValida = validarDataMatriculaDataEmissao(
                    dataMatricula,
                    $scope.certidaoCartorial.dataEmissao
                );
                if (
                    $scope.uploadCertidaoCartorial.$valid &&
                    dataMatriculaValida &&
                    validarExtensaoArquivo($scope.certidaoCartorial.certidao)
                ) {
                    if (validarCertidaoUnica($scope.certidaoCartorial)) {
                        salvarArquivo($scope.certidaoCartorial);
                        $scope.certidaoCartorialSubmitted = false;
                    }
                }
            };

            var validarDataMatriculaDataEmissao = function (
                dataMatricula,
                dataEmissao
            ) {
                var dataM = new Date(dataMatricula);
                var dataE = new Date(dataEmissao);
                if (dataEmissao < dataMatricula) {
                    MensagensService.exibirMensagemErro(
                        'A Data de Emissão não pode ser anterior a Data da' +
                        ' Matrícula/Transcrição informada.'
                    );
                    return false;
                }
                return true;
            };

            var salvarArquivo = function (certidao) {
                CertidaoCartorial.save(
                    certidao,
                    function (response) {
                        $scope.certidoesCartorial.push(response);
                        $scope.certidaoCartorial = {};
                    },
                    function (error) {
                        MensagensService.exibirMensagemErro(error.data.message);
                    }
                );
            };

            var validarCertidaoUnica = function (objCertidao) {
                if ($scope.certidoesCartorial.length <= 0) {
                    return true;
                } else {
                    for (var i = 0; i < $scope.certidoesCartorial.length; i++) {
                        if (
                            $scope.certidoesCartorial[i].nome == $scope.certidaoCartorial.nome
                        ) {
                            MensagensService.exibirMensagemErro(
                                'O documento selecionado já foi enviado.'
                            );
                            return false;
                        }
                    }
                    return true;
                }
            };

            $scope.removerCertidao = function (index) {
                if (confirm('Tem certeza que deseja excluir este arquivo?')) {
                    $scope.certidoesCartorial.splice(index, 1);
                    MensagensService.exibirMensagemSucesso(
                        'Certidão excluída com sucesso.'
                    );
                }
            };

            var validarExtensaoArquivo = function (arquivo) {
                if (getExtensao(arquivo.name) == '.pdf') return true;
                else if (getExtensao(arquivo.name) == '.png') return true;
                else if (getExtensao(arquivo.name) == '.jpg') return true;
                else {
                    MensagensService.exibirMensagemErro(
                        'Formato de arquivo inválido. São permitidos apenas arquivos do tipo' +
                        ' PDF, JPG ou PNG.'
                    );
                    return false;
                }
            };

            $scope.hoje = new Date();

            var getExtensao = function (nomeArquivo) {
                return nomeArquivo.substring(nomeArquivo.length - 4);
            };

            $scope.confirmar = function () {
                $mdDialog.hide();
            };

            $scope.visualizarCertidao = function (id) {
                ExibirCertidao.visualizarCertidao(id);
            };
        }

        $scope.visualizarCertidao = function (id) {
            ExibirCertidao.visualizarCertidao(id);
        };

        /*UNIDADE AUTONOMA*/

        self.limpaDadosRecuperadosUnidadeAutonoma = function () {
            self.imovel.tipoImovel = self.modelTipoImovel;
            self.imovel.tipoUnidadeAutonoma = undefined;
            self.imovel.pontoImovel = undefined;
            self.imovel.uuid = undefined;
            self.imovel.enderecamento = {};
            self.imovel.enderecamento.pais = Dominio.Constantes.Brasil;
            self.limpaObjeto(self.imovel.terreno);
            self.imovel.terreno.unidadeMedidaCadastral = 'm2';
            self.imovel.terreno.unidadeMedidaCartorial = 'm2';
            self.imovel.naturezaImovel = undefined;
            NotificadorService.notify(
                $scope,
                null,
                notifiers.REMOVER_PONTO_EDIFICACOES_TERRENO_UA
            );
        };

        self.limpaObjeto = function (obj) {
            for (var p in obj) {
                if (obj.hasOwnProperty(p)) {
                    obj[p] = undefined;
                }
            }
        };

        $scope.$on('editarVoltarUnidadeAutonoma', function (event, args) {
            self.showFormularioUnidadeAutonoma = function () {
                return true;
            };
            self.showMapaEdificacaoCadastrada = false;
        });

        self.showFormularioUnidadeAutonoma = function () {
            return (
                self.tipoCadastroEdificacaoUnidadeAutonoma !== 'EXISTENTE' ||
                (self.tipoCadastroEdificacaoUnidadeAutonoma === 'EXISTENTE' &&
                    self.imovel.unidadeAutonoma.edificacao.id)
            );
        };

        self.cancelarSelecaoEdificacaoUnidadeAutonoma = function () {
            self.tipoCadastroEdificacaoUnidadeAutonoma = '';
        };
        self.plantaUnidade = {};

        self.removerPlanta = function (index) {
            var confirm = $mdDialog
                .confirm()
                .textContent('Deseja realmente excluir este arquivo?')
                .ariaLabel('Excluir Imagem')
                .targetEvent(index)
                .cancel('Não')
                .ok('Excluir');

            $mdDialog.show(confirm).then(
                function () {
                    self.imovel.unidadeAutonoma.plantasUnidade.splice(index, 1);
                    MensagensService.exibirMensagemSucesso(
                        'Planta excluída com sucesso.'
                    );
                },
                function () {
                    $mdDialog.cancel();
                }
            );
        };

        self.resetarPlantaUnidade = function () {
            self.plantaUnidade = {};
        };
        self.showUploadPlantaUnidadeDialog = function (ev) {
            if (self.imovel.unidadeAutonoma == undefined) {
                self.imovel.unidadeAutonoma = {};
                self.imovel.unidadeAutonoma.plantasUnidade = [];
            } else {
                if (self.imovel.unidadeAutonoma.plantasUnidade == undefined) {
                    self.imovel.unidadeAutonoma.plantasUnidade = [];
                }
            }
            $mdDialog.show({
                controller: uploadPlantaUnidadeDialogController,
                templateUrl: 'scripts-cadastro-imovel/pages/unidade-autonoma/view/dialog-upload-planta-unidade.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose: true,
                locals: {
                    plantaUnidade: self.plantaUnidade,
                    plantasUnidade: self.imovel.unidadeAutonoma.plantasUnidade,
                },
            });
        };

        var uploadPlantaUnidadeDialogController = uploadPlantaUnidadeDialogController;

        uploadPlantaUnidadeDialogController.$inject = [
            '$scope',
            '$http',
            '$mdDialog',
            'PlantaUnidade',
            'ExibirPlantaUnidade',
            'plantaUnidade',
            'plantasUnidade',
        ];

        function uploadPlantaUnidadeDialogController(
            $scope,
            $http,
            $mdDialog,
            PlantaUnidade,
            ExibirPlantaUnidade,
            plantaUnidade,
            plantasUnidade
        ) {
            $scope.plantaUnidade = plantaUnidade;

            $scope.plantasUnidade = plantasUnidade;

            $scope.fechar = function () {
                $mdDialog.cancel();
            };

            $scope.incluir = function () {
                if (
                    validarPlantaUnica($scope.plantaUnidade) &&
                    $scope.uploadPlanta.$valid
                ) {
                    salvarArquivo($scope.plantaUnidade);
                } else {
                    MensagensService.exibirMensagemErro(
                        'Necessário marcar o ponto de localização do imóvel'
                    );
                }
            };

            var salvarArquivo = function (planta) {
                PlantaUnidade.save(
                    planta,
                    function (response) {
                        $scope.plantasUnidade.push(response);
                        $scope.plantaUnidade = {};
                        self.imovel.unidadeAutonoma.plantasUnidade = $scope.plantasUnidade;
                    },
                    function (error) {
                        MensagensService.exibirMensagemErro(error.data.message);
                    }
                );
            };

            var validarPlantaUnica = function (objCertidao) {
                if ($scope.plantasUnidade == undefined) {
                    return true;
                } else {
                    for (var i = 0; i < $scope.plantasUnidade.length; i++) {
                        if (
                            $scope.plantasUnidade[i].planta.name ==
                            $scope.plantaUnidade.planta.name
                        ) {
                            MensagensService.exibirMensagemErro(
                                'O documento selecionado já foi enviado.'
                            );
                            return false;
                        }
                    }
                    return true;
                }
            };

            $scope.removerPlanta = function (index) {
                if (confirm('Tem certeza que deseja excluir este arquivo?')) {
                    $scope.plantasUnidade.splice(index, 1);
                    MensagensService.exibirMensagemSucesso(
                        'Planta excluída com sucesso.'
                    );
                }
            };

            $scope.hoje = new Date();

            $scope.confirmar = function () {
                $mdDialog.hide();
            };
            $scope.visualizarPlanta = function (id) {
                ExibirPlantaUnidade.visualizarPlanta(id);
            };
        }

        self.visualizarPlanta = function (id) {
            ExibirPlantaUnidade.visualizarPlanta(id);
        };
        self.idSenadoFederalAcessos = 2;
        self.idTcuAcessos = 3;
        self.idMinisterioDefesaAcessos = 17;
        self.idSenadoFederalUgAcessos = 64;
        self.ugProprietario = {};
        self.ugProprietarioParcial = {};
        self.ugProprietarioTitular = {};
        self.orgaoEntidadeProprietario = {};
        self.orgaoEntidadeTitular = {};
        self.orgaoEntidadeAnterior = {};
        self.ugProprietarioAnterior = {};
        self.orgaoEntidadeProprietarioParcial = {};
        self.ugsTitular = undefined;

        self.ugsAnterior = undefined;

        self.isFracionamento = function () {
            return (
                $scope.idImovelOrigem &&
                $scope.imovel.incorporacao.tipoIncorporacao.id === ID_POR_FRACIONAMENTO
            );
        };

        self.isFusao = function () {
            return (
                $scope.imovel.incorporacao.tipoIncorporacao &&
                $scope.imovel.incorporacao.tipoIncorporacao.id === ID_POR_FUSAO
            );
        };

        self.hasTabBenfeitoria = function () {
            return tabsIncorporacao.hasTabBenfeitoria();
        };

        self.isTipoProprietarioParcialAutarquiaFundacao = function () {
            if ($scope.imovel.proprietario) {
                if ($scope.imovel.proprietario.tipoProprietarioParcial) {
                    return (
                        $scope.imovel.proprietario.tipoProprietarioParcial.id ===
                        ID_AUTARQUIAFUNDACAO_PARCIAL &&
                        $scope.imovel.proprietario.isIntegralmente != 'true'
                    );
                }
            }
        };

        self.isTipoProprietarioAnteriorTerceiros = function () {
            if (
                $scope.imovel.proprietarioAnterior &&
                $scope.imovel.proprietarioAnterior.tipoProprietarioReal
            ) {
                return (
                    $scope.imovel.proprietarioAnterior.tipoProprietarioReal.id ===
                    ID_TERCEIROS_PROPRIETARIO_REAL
                );
            }
        };

        function atualizarAcorddion() {
            $scope.erros = angular.element('.ng-invalid');
            for (var erro = 0, len = $scope.erros.length; erro < len; erro++) {
                var scope = $scope.erros[erro];
                if (scope.tagName !== 'FORM' && scope.tagName !== 'NG-FORM') {
                    var escopoElemento = angular.element(scope).scope();

                    if (escopoElemento && escopoElemento.$pane) {
                        escopoElemento.$pane.expand();
                    } else if (scope.parentNode && scope.parentNode.parentElement) {
                        if (scope.parentNode.parentElement.id === 'coordenadasForm') {
                            abrirAcordionPonto();
                        } else if (scope.parentNode.parentElement.id === 'secaoDadosGeraisDoImovel' || (scope.parentNode.parentElement.parentElement && scope.parentNode.parentElement.parentElement.id === 'secaoDadosGeraisDoImovel')) {
                            abrirAcordionDetalhesDoImovel();
                        } else if (seSecaoDadosIncorporacao(scope)) {
                            abrirAcordionDadosIncorporacao();
                        }
                    }
                }
            }
        }

        function seSecaoDadosIncorporacao(scope) {
            if (scope.parentNode.id === 'secaoDadosIncorporacao' ||
                (scope.parentNode.parentElement.id === 'secaoDadosIncorporacao') ||
                (scope.parentNode.parentElement.parentElement && scope.parentNode.parentElement.parentElement.id === 'secaoDadosIncorporacao') ||
                (scope.parentNode.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.id === 'secaoDadosIncorporacao') ||
                (scope.parentNode.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.id === 'secaoDadosIncorporacao') ||
                (scope.parentNode.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement.id === 'secaoDadosIncorporacao') ||
                (scope.parentNode.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement.id === 'secaoDadosIncorporacao') ||
                (scope.parentNode.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement && scope.parentNode.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement.id === 'secaoDadosIncorporacao')) {
                return true;
            }

            return false;
        }

        function isCDRUGratuito() {
            return self.imovel.incorporacao.tipoContratoPessoal
                && self.imovel.incorporacao.tipoContratoPessoal.id == 8
                && self.imovel.incorporacao.isCdruOnerosa == false;
        }

        function isDireitoSuperficieGratuito() {
            return self.imovel.incorporacao.tipoContratoPessoal
                && self.imovel.incorporacao.tipoContratoPessoal.id == 7
                && self.imovel.incorporacao.isDireitoSuperficieOneroso == false;
        }

        function validarFormularioDadosGerais() {

            if ($scope.forms.imovelForm.$valid) {
                if (self.isFusao() && self.imovel.ripsFusao.length < 2) {
                    MensagensService.exibirMensagemErro(
                        'É necessário a inclusão de pelo menos 2 RIPs de imóveis remembrados'
                    );
                    abrirAcordionDadosIncorporacao();
                    return false;
                }

                if (self.isAquisicao() && !hasTituloAquisitivo()) {
                    MensagensService.exibirMensagemErro(
                        'É necessário a inclusão de, pelo menos, um título aquisitivo'
                    );
                    abrirAcordionDadosIncorporacao();
                    return false;
                }

                //Título aquisitivo obrigatório para imóveis de terceiros e CDRU gratuita e Direito de Superfície Gratuito.
                if (isImoveisDeTerceiros()) {
                    if (isCDRUGratuito() || isDireitoSuperficieGratuito()) {
                        if (!hasTituloAquisitivo()) {
                            MensagensService.exibirMensagemErro('É necessário a inclusão de pelo menos um título aquisitivo');
                            abrirAcordionDadosIncorporacao();
                            return false;
                        }
                    }
                }

                // if (self.imovel.tipoProcessoSei === 'processoExistente' && !self.imovel.linkProcessoSei && self.ugUsuarioLogado.includes('Patrimônio da União')) {
                //     MensagensService.exibirMensagemErro(
                //         'O número do processo SEI é inválido.'
                //     );
                //     abrirAcordionDadosIncorporacao();
                //     return false;
                // }

                if (!isOriginalmenteUniao() && !isProcessoIncorporacao()
                    && !TipoImovelFactory.isEspelhoDagua(self.imovel.tipoImovel)) {

                    if (!hasMatriculaIncorporacao()) {
                        MensagensService.exibirMensagemErro(
                            'É necessário informar uma matrícula'
                        );
                        abrirAcordionDadosIncorporacao();
                        return false;
                    }

                    if (hasMatriculaIncorporacao() && !hasCertidaoCartorial()) {
                        MensagensService.exibirMensagemErro(
                            'É necessário incluir uma certidão cartorial'
                        );
                        abrirAcordionDadosIncorporacao();
                        return false;
                    }
                }
            } else {
                MensagensService.exibirMensagemCamposObrigatorios();

                atualizarAcorddion();
                abrirAcordionDadosIncorporacao();

                return false;
            }
            return true;
        }

        function abrirAcordionPonto() {
            document.getElementById('dadosLocalizacaoImovel').classList.add("is-expanded");
            document.getElementById('dadosLocalizacaoImovelContent').style.maxHeight = 'none';
            document.getElementById('coordenadasDoPonto').classList.add("is-expanded");
            document.getElementById('coordenadasDoPontoContent').style.maxHeight = 'none';
        }

        function abrirAcordionDetalhesDoImovel() {
            document.getElementById('dadosLocalizacaoImovel').classList.add("is-expanded");
            document.getElementById('dadosLocalizacaoImovelContent').style.maxHeight = 'none';
            document.getElementById('detalhesDoImovel').classList.add("is-expanded");
            document.getElementById('detalhesDoImovelContent').style.maxHeight = 'none';
        }

        function abrirAcordionDadosIncorporacao() {
            document.getElementById('dadosIncorporacao').classList.add("is-expanded");
            document.getElementById('dadosIncorporacaoContent').style.maxHeight = 'none';
        }

        function abrirAcordionDescricaoCadastral() {
            document.getElementById('descricaoCadastral').classList.add("is-expanded");
            document.getElementById('descricaoCadastralContent').style.maxHeight = 'none';
        }

        function abrirAcordionProprietario() {
            document.getElementById('secaoProprietario').classList.add("is-expanded");
            document.getElementById('secaoProprietarioContent').style.maxHeight = 'none';
        }

        self.submitFormularioDadosGerais = function () {
            if (validarFormularioDadosGerais()) {
                $scope.selectTab(1);
            } else {
                $scope.forms.imovelForm.$setSubmitted();
            }
        };

        self.submitFormularioLocalizacao = function () {
            if (validarFormularioLocalizacao()) {
                $scope.selectTab(0);
            } else {
                $scope.forms.localizacaoForm.$setSubmitted();
            }
        };

        self.submitFormularioAcessaoBenfeitoria = function () {

            if (
                self.imovel.benfeitoriaPertenceAoProprietario === 'Sim' &&
                !TipoImovelFactory.isEspelhoDagua(self.imovel.tipoImovel)
            ) {
                if (!self.imovel.benfeitorias ||
                    self.imovel.benfeitorias.length === 0
                ) {
                    MensagensService.exibirMensagemErro(
                        'É necessário adicionar pelo menos uma benfeitoria'
                    );
                    return;
                }
            }
            if (self.imovel.benfeitoriaPertenceAoProprietario === undefined) {
                $scope.submittedBenfeitoria = true;
                MensagensService.exibirMensagemCamposObrigatorios();
            } else {
                //atualizarAcorddion();

                $scope.selectTab(5);
            }
        };

        self.isPertenceProprietario = function () {
            return self.imovel.benfeitoriaPertenceAoProprietario === 'Sim';
        };

        self.submitFormularioUnidadeAutonoma = function () {
            var respostaUnidadeAutonoma = validarFormularioUnidadeAutonoma();
            if (respostaUnidadeAutonoma.isValid) {
                $scope.selectTab(5);
            } else {
                $scope.submittedUnidadeAutonoma = true;
                $scope.forms.adicionarUnidadeAutonomaForm.$setSubmitted();
                MensagensService.exibirMensagemErro(respostaUnidadeAutonoma.message);
            }
        };

        self.tipoVetorizacao = function () {
            return self.imovel.unidadeAutonoma.edificacao.tipoGeometria === Dominio.Constantes.TiposDeDesenho.VETORIZACAO;
        };

        function validarFormularioUnidadeAutonoma() {
            var response = {
                isValid: true,
                message: '',
            };
            if ($scope.forms.adicionarUnidadeAutonomaForm.$valid) {
                if (!TipoImovelFactory.isGaragem(self.imovel.tipoImovel) &&
                    !self.imovel.unidadeAutonoma.edificacao.geometria
                ) {
                    response.isValid = false;
                    response.message = 'Favor informar a geometria da edificação';
                } else if (!TipoImovelFactory.isGaragem(self.imovel.tipoImovel) &&
                    !self.geometriaUnidadeAutonomaValida
                ) {
                    response.isValid = false;
                    response.message = 'Geometria inválida';
                }
            } else {
                atualizarAcorddion();

                response.isValid = false;
                response.message = 'Necessário preencher os campos obrigatórios';
            }
            return response;
        }

        self.submitFormularioTerreno = function () {

            if (!self.imovel.enderecamento.uf) {
                self.imovel.enderecamento.uf = self.imovel.enderecamento.ufImovel;
                self.imovel.enderecamento.municipio = self.imovel.enderecamento.municipioImovel;
            }

            var isFormValid = $scope.forms.terrenoForm.$valid,
                possuiGeometria = !!self.imovel.terreno.geometriaTerreno,
                isGeometriaValida = self.geometriaTerrenoCadastralValida,
                isPontoNaInsercao = self.pontoNaInsercao,
                isCavidadeNaturalSubterranea = TipoImovelFactory.isCavidadeNaturalSubterranea(
                    self.imovel.tipoImovel
                );

            var respostaValidacaoTerreno = TerrenoService.validarFormularioTerreno(
                isFormValid,
                possuiGeometria,
                isGeometriaValida,
                isPontoNaInsercao,
                isCavidadeNaturalSubterranea
            );

            if (self.imovel.proprietario.tipoProprietarioReal.descricao === TIPO_PROPRIETARIO_UNIAO) {
                self.imovel.cnpjProprietario = CNPJ_UNIAO;
            }

            if (respostaValidacaoTerreno.isValid) {
                if (
                    TipoImovelFactory.isGlebaTerrenoLoteComEdificacao(
                        self.imovel.tipoImovel
                    ) ||
                    TipoImovelFactory.isEspelhoDagua(self.imovel.tipoImovel)
                ) {
                    $scope.selectTab(2);
                } else if (
                    TipoImovelFactory.isUnidadeAutonoma(self.imovel.tipoImovel)
                ) {
                    $scope.selectTab(3);
                } else {
                    $scope.selectTab(5);
                }
            } else {
                //atualizarAcorddion();
                abrirAcordionDescricaoCadastral();

                $scope.submittedTerreno = true;
                $scope.forms.terrenoForm.$setSubmitted();
                MensagensService.exibirMensagemErro(respostaValidacaoTerreno.message);
            }
        };

        function validarFormularioLocalizacao() {
            if ($scope.forms.localizacaoForm.$valid) {

                self.imovel.enderecamento.ufImovel = self.imovel.enderecamento.uf;
                self.imovel.enderecamento.municipioImovel = self.imovel.enderecamento.municipio;

                if (!self.imovel.pontoImovel) {
                    MensagensService.exibirMensagemErro(
                        'Necessário marcar o ponto de localização do imóvel'
                    );
                    abrirAcordionPonto();

                    return false;
                }
            } else {
                MensagensService.exibirMensagemCamposObrigatorios();

                atualizarAcorddion();

                /*if(angular.element('md-select.ng-invalid')){

                }else{
                  angular.element('input.ng-invalid').first().trigger("focus");
                  console.log('Passei 2');
                }*/
                return false;
            }
            return true;
        }

        function hasMatriculaIncorporacao() {
            return $scope.imovel.incorporacao.matriculas.length > 0;
        }

        function hasTituloAquisitivo() {
            return $scope.imovel.titulos.length > 0;
        }

        function hasCertidaoCartorial() {
            return (
                $scope.imovel.incorporacao.certidoesCartoriais &&
                $scope.imovel.incorporacao.certidoesCartoriais.length > 0
            );
        }

        $scope.$on('retornoVerificaMapaBing', function (event, args) {
            var anyThing = args.any;

            anyThing.getLayers().forEach(function (lyr) {
                if (lyr.get('name') === 'bingMapa' && lyr.getVisible()) {
                    lyr.setVisible(!lyr.getVisible());
                    NotificadorService.notify(
                        $scope,
                        lyr.getVisible(),
                        notifiers.ALTERAR_VISIBILIDADE_BING
                    );
                }
            });
        });

        $scope.showModalValidacao = function (ev, form, tab, fn) {

            if (self.imovel.incorporacao.tipoIncorporacao.id === ID_IMOVEL_DE_TERCEIROS
                && self.imovel.proprietariosTerceiros.length === 0) {
                MensagensService.exibirMensagemCamposObrigatorios("Necessário informar Terceiro.");
                return;
            }

            $rootScope.$broadcast('broadVerificaMapaBing');
            //noinspection JSAnnotator
            eval.errorsForms = [];
            $scope.submittedTerreno = false;

            //domtoimage.toBlob(document.getElementById('fotoPontoImovel')).then(
            html2canvas(document.getElementById('fotoPontoImovel')).then(function (canvas) {
                canvas.toBlob(function (blob) {
                    var file = new File([blob], 'mapaimg.png', {
                        type: 'image/png',
                        lastModified: Date.now(),
                    });

                    ArquivoService.uploadArquivo(file).then(function (response) {
                        $scope.imovel.imagemMapa = {};

                        $scope.imovel.imagemMapa.arquivo = response.data.byteArray;
                        $scope.imovel.imagemMapa.nomeOriginal = response.data.nome;
                        $scope.imovel.imagemMapa.tipoArquivo = response.data.tipo;

                        $scope.imovel.unidadeAutonoma.fracaoIdealCartorial =
                            $rootScope.fracaoIdealCartorialNum;
                        $scope.imovel.unidadeAutonoma.fracaoIdealCadastral =
                            $rootScope.fracaoIdealCadastralNum;

                        angular.forEach($scope.imovel.incorporacao.matriculas, function (
                            value
                        ) {
                            if (value.uf.sigla != undefined) {
                                value.uf = value.uf.sigla;
                            }
                        });

                        if (tab === 2 || tab === 3 || tab === 5) {
                            if (!TipoImovelFactory.isCavidadeNaturalSubterranea(
                                self.imovel.tipoImovel
                            )) {
                                if (!$scope.imovel.terreno.geometriaTerreno &&
                                    !$scope.imovel.terreno.geometriaTerrenoCartorial
                                ) {
                                    MensagensService.exibirMensagemErro(
                                        'É necessário informar a geometria do terreno'
                                    );
                                    abrirAcordionDescricaoCadastral();
                                    return;
                                }
                                if (!self.pontoNaInsercao && self.imovel.benfeitorias.length === 0) {

                                    MensagensService.exibirMensagemErro(
                                        'O ponto do imóvel não se encontra sobre uma das linhas do vértice do polígono do terreno.'
                                    );
                                    return;
                                }

                            }
                        }
                        if (tab === 5) {
                            if (
                                self.imovel.benfeitoriaPertenceAoProprietario === 'Sim' &&
                                !TipoImovelFactory.isEspelhoDagua(self.imovel.tipoImovel)
                            ) {
                                if (!self.imovel.benfeitorias ||
                                    self.imovel.benfeitorias.length === 0
                                ) {
                                    MensagensService.exibirMensagemErro(
                                        'É necessário adicionar pelo menos uma benfeitoria'
                                    );
                                    return;
                                }
                            }
                        }

                        if (
                            (form && $scope.selectedIndex != 2) ||
                            $scope.imovel.pontoImovel == undefined ||
                            ($scope.selectedIndex > 0 &&
                                $scope.imovel.terreno.geometriaTerreno == undefined &&
                                $scope.imovel.terreno.geometriaTerrenoCartorial == undefined) ||
                            ($scope.selectedIndex === 3 &&
                                $scope.imovel.unidadeAutonoma == undefined) ||
                            $scope.selectedIndex == 2
                        ) {
                            if ($scope.selectedIndex == -1) {
                                $scope.verificaObrigatoriosLocalizacao();
                                $scope.submittedLocalizacao = true;
                            } else if ($scope.selectedIndex == 0) {
                                $scope.verificaObrigatoriosImovel();
                                $scope.submittedImovel = true;
                            } else if ($scope.selectedIndex == 1) {
                                $scope.verificaObrigatoriosImovel();
                                angular.forEach(
                                    $scope.forms.terrenoForm.$error.required,
                                    function (value, key) {
                                        if (value.$name && value.$name != '') {
                                            $scope.forms.terrenoForm.$setSubmitted();
                                            eval.errorsForms.push(value.$name);
                                        }
                                    }
                                );

                                if (
                                    $scope.imovel.terreno.geometriaTerreno == undefined &&
                                    $scope.imovel.terreno.geometriaTerrenoCartorial ==
                                    undefined &&
                                    ($scope.tipoDesenhoCartorial === 333 ||
                                        $scope.tipoDesenhoCadastral === 333)
                                ) {
                                    eval.errorsForms.push('geometriaTerreno');
                                }

                                if (
                                    $scope.imovel.terreno.geometriaTerreno == undefined &&
                                    $scope.imovel.terreno.geometriaTerrenoCartorial ==
                                    undefined &&
                                    ($scope.tipoDesenhoCartorial === 222 ||
                                        $scope.tipoDesenhoCadastral === 222)
                                ) {
                                    eval.errorsForms.push('shapefileNaoCarregado');
                                }
                            } else if ($scope.selectedIndex == 3) {
                                $scope.submittedUnidadeAutonoma = true;
                                $scope.verificaObrigatoriosUnidadeAutonoma();
                            } else if ($scope.selectedIndex === 5) {
                                $scope.submittedProprietario = true;
                                $scope.verificaObrigatoriosProprietario();
                            }
                            //noinspection JSAnnotator
                            if (eval.errorsForms.length > 0) {
                                //noinspection JSAnnotator
                                if (tab == 2 || tab == 3 || tab == 5) {
                                    $scope.submittedTerreno = true;
                                }
                                //noinspection JSAnnotator
                                if ($scope.imovel.pontoImovel == undefined && tab == 0) {
                                    MensagensService.exibirMensagemErro(
                                        'Necessário marcar o ponto de localização do imóvel'
                                    );
                                    abrirAcordionPonto();
                                } else {
                                    //noinspection JSAnnotator
                                    if (
                                        (($scope.imovel.terreno.geometriaTerreno == undefined &&
                                            $scope.submittedTerreno == true) ||
                                            ($scope.imovel.unidadeAutonoma == undefined &&
                                                self.unidadeAutonomaSubmitted == true)) &&
                                        tab != 1
                                    ) {
                                        MensagensService.exibirMensagemErro(
                                            'Necessário informar uma geometria.'
                                        );
                                        abrirAcordionDescricaoCadastral();
                                    } else {
                                        abrirAcordionProprietario();
                                        MensagensService.exibirMensagemCamposObrigatorios();
                                    }
                                }
                            } else {
                                if (
                                    $scope.selectedIndex == 1 &&
                                    !$scope.forms.terrenoForm.fracaoIdealImovelCadastral.$valid
                                ) {
                                    MensagensService.exibirMensagemErro(
                                        'O Campo Fração Ideal do Imóvel está inválido'
                                    );
                                } else if (fn == null) {
                                    $scope.selectTab(tab);
                                } else {
                                    self.salvarImovel();
                                }
                            }
                        } else {
                            if (
                                $scope.selectedIndex == 1 &&
                                !$scope.forms.terrenoForm.fracaoIdealImovelCadastral.$valid
                            ) {
                                MensagensService.exibirMensagemErro(
                                    'O Campo Fração Ideal do Imóvel está inválido'
                                );
                            } else if (fn == null) {
                                if (self.pontoNaInsercao || tab != 2) {
                                    $scope.selectTab(tab);
                                } else {
                                    MensagensService.exibirMensagemErro(
                                        'O ponto do imóvel não se encontra sobre uma das linhas do vértice do polígono do terreno.'
                                    );
                                    abrirAcordionPonto();
                                }
                            } else {
                                self.salvarImovel();
                            }
                        }
                    });
                });
            },
                function (error) {
                    console.log("Erro upload imagem do mapa!", error);
                });
        };

        // Função para mostrar o modal de confirmação da UG Responsável
        self.mostrarModalConfirmarUG = function () {
            return $mdDialog.show({
                controller: ["$scope", "$mdDialog", "proprietario", "ugResponsavel", function ($scope, $mdDialog, proprietario, ugResponsavel) {
                    $scope.proprietario = proprietario;
                    $scope.ugResponsavel = ugResponsavel;

                    $scope.fechar = function () {
                        $mdDialog.cancel();
                    };

                    $scope.confirmar = function () {
                        $mdDialog.hide();
                    };
                }],
                templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-confirmar-ug-responsavel.html',
                parent: angular.element(document.body),
                clickOutsideToClose: false,
                locals: {
                    proprietario: $scope.imovel.proprietario,
                    ugResponsavel: $scope.imovel.proprietario.ugresponsavel
                }
            });
        };

        self.confirmaSalvarImovel = function () {
            // Primeiro, mostra o modal de confirmação da UG Responsável
            self.mostrarModalConfirmarUG().then(function () {
                // Após confirmar a UG, mostra o modal de confirmação do proprietário
                var confirm = $mdDialog
                    .confirm({ focusOnOpen: false })
                    .textContent(
                        'Você está cadastrando um imóvel para o proprietário ' +
                        $scope.imovel.proprietario.tipoProprietarioReal.descricao +
                        '. Deseja continuar?'
                    )
                    .ariaLabel('Cadastrar')
                    .cancel('Não')
                    .ok('Cadastrar');

                $mdDialog.show(confirm).then(function () {

                    $mdDialog.show(
                        $mdDialog.alert({
                            templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/modals/progress.tmpl.html'
                        })
                    );

                    if (!self.isFusao()) {
                        NotificadorService.notify(
                            $scope,
                            null,
                            NOTIFICADORES.LIMPA_DADOS_RIPS_FUSAO
                        );
                    }

                    angular.forEach(self.imovel.incorporacao.matriculas, function (matricula) {
                        if (typeof matricula.uf !== 'string') {
                            matricula.uf = matricula.uf.sigla;
                        }
                    });

                    //console.log('=== OBJETO IMÓVEL SENDO ENVIADO ===', self.imovel);

                    Imovel.save(
                        self.imovel,
                        function success(data, status, headers, config, params) {
                            homologarEnviarDestinacaoService
                                .homologarEnviarDestinacao(data)
                                .then(
                                    function (response) {
                                        var imovelCadastrado = response.data;
                                        $scope.imovelCriado = imovelCadastrado;
                                        $mdDialog.hide();
                                        if ($rootScope.usuarioLogado.permissoes.includes("CAD_IMOVEL.HOMOLOGAR_IMOVEL")) {
                                            MensagensService.exibirMensagemSucesso("Imóvel cadastrado com sucesso! Rip: " + imovelCadastrado.rip);
                                        } else {
                                            MensagensService.exibirMensagemSucesso("Imóvel cadastrado com sucesso!");
                                        }
                                        if ($rootScope.usuarioLogado.permissoes.indexOf('CAD_IMOVEL.HOMOLOGAR_IMOVEL') !== -1) {
                                            self.enviarDocumentosSei();
                                        } else {
                                            $timeout(function () {
                                                $state.go('INCORPORACAO.detalharImovel', { id: $scope.imovelCriado.id });
                                            }, 1000);
                                        }
                                    },
                                    function (error) {
                                        $mdDialog.hide();
                                        if (error.data.errors != null && error.data.errors != undefined && error.data.errors.length > 0) {
                                            MensagensService.exibirMensagemErro(error.data.errors[0].defaultMessage);
                                        } else if (error.data != null && error.data.message) {
                                            MensagensService.exibirMensagemErro(error.data.message);
                                           if (error.data.message.includes('não pode ser enviado para destinação')){
                                            $state.go('INCORPORACAO.imovelConsultar');
                                           }
                                        } else {
                                            MensagensService.exibirMensagemErro(error);
                                        }
                                    }
                                );
                        },
                        function error(error) {
                            $mdDialog.hide();
                            MensagensService.exibirMensagemErro(error.data.message);
                        }
                    );
                },
                    function () {
                        $mdDialog.cancel();
                    }
                );
            });
        };

        self.enviarDocumentosSei = function () {
            $mdDialog.show({
                controller: enviarDocumentosSeiDialogController,
                templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-enviar-documentos-sei.html',
                parent: angular.element(document.body),
                clickOutsideToClose: false,
                locals: {
                    imovel: $scope.imovelCriado
                }
            });
        };


        // self.abrirModalUfMunicipio = function() {
        //   MensagensService.exibirMensagemCamposObrigatorios("UF não identificada pelo Geoserviço. Favor informar a UF do Imóvel.");
        //   $mdDialog.show({
        //       controller: modalUfMunicipioController,
        //       templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-comboUf.html',
        //       parent: angular.element(document.body),
        //       clickOutsideToClose: false,
        //       locals: {imovel: $scope.imovelCriado}
        //   });

        // }

        // var modalUfMunicipioController = modalUfMunicipioController;

        // modalUfMunicipioController.$inject = [
        //     '$scope',
        //     '$mdDialog',
        //     'imovel',
        //     'UfsService',
        //     'MunicipioService'
        // ];

        // function modalUfMunicipioController(
        //     $scope,
        //     $mdDialog,
        //     imovel,
        //     UfsService,
        //     MunicipioService
        // ) {

        //   $scope.imovel = imovel;
        //   $scope.ufs = [];
        //   $scope.municipios = [];

        //   carregauf();

        //    if (imovel.enderecamento.uf) {
        //      buscaMunicipio(imovel.enderecamento.uf);
        //    }
        //   function carregauf () {
        //     $scope.ufs = self.jurisdicoes;
        //   }

        //   function buscaMunicipio (uf2) {
        //     $scope.municipios = [];
        //     $scope.municipios = MunicipioService.TodosMunicipios.query({ uf: uf2 });
        //     $scope.municipios.nome = $scope.imovel.enderecamento.municipio;
        //   }

        //   $scope.confirmarUf = function() {
        //      console.log(imovel.enderecamento.uf);
        //      console.log(imovel.enderecamento.municipio);
        //      $mdDialog.hide();
        //      //self.confirmaSalvarImovel();
        //     };

        //   // $scope.buscaMunicipiosUF = function (uf2) {
        //   //     $scope.municipios = [];
        //   //     $scope.municipios = MunicipioService.TodosMunicipios.query({ uf: uf2 });
        //   // }
        // }

        var enviarDocumentosSeiDialogController = enviarDocumentosSeiDialogController;

        enviarDocumentosSeiDialogController.$inject = [
            '$scope',
            '$http',
            '$mdDialog',
            'imovel'

        ];

        function enviarDocumentosSeiDialogController(
            $scope,
            $http,
            $mdDialog,
            imovel
        ) {
            $scope.imovel = imovel;
            $scope.envioSei = {};
            $scope.envioSei.despachoConclusao = false;
            $scope.envioSei.folhaRosto = false;
            $scope.envioSei.memorialDescritivo = false;
            $scope.envioSei.shapefileTerrenoCartorial = false;
            $scope.envioSei.shapefileTerrenoCadastral = false;
            $scope.envioSei.shapefileBenfeitorias = false;
            $scope.envioSei.shapefileEdificacao = false;
            $scope.envioSei.tituloAquisitivo = false;
            $scope.envioSei.documentoCartorial = false;
            $scope.envioSei.arquivosComplementares = false;
            $scope.envioSei.documentacaoEdificacao = false;
            $scope.envioSei.arquivoPlanta = false;
            $scope.showEdificacaoAutonoma = false;
            $scope.showEdificacaoBenfeitoria = false;
            $scope.showPlantaBenfeitoria = false;
            $scope.showPlantaAutonoma = false;

            apresentaDocumentacaoEdificacao();
            apresentaDocumentacaoBenfeitoria();
            apresentaPlantasBenfeitoria();
            apresentaPlantaEdificacao();

            $scope.fechar = function () {
                $timeout(function () {
                    $state.go('INCORPORACAO.detalharImovel', { id: $scope.imovel.id });
                }, 1000);
                $mdDialog.cancel();
            };

            $scope.formatarNumero = function (numero) {
                return numero.replace(/(\d{5})(\d{6})(\d{4})(\d{2})/, "$1.$2/$3-$4");
            }

            function apresentaDocumentacaoBenfeitoria() {
                if (imovel.benfeitorias != null && imovel.benfeitorias != undefined) {
                    angular.forEach(imovel.benfeitorias, function (benfeitoria) {
                        if (benfeitoria.edificacao != null && benfeitoria.edificacao != undefined) {
                            angular.forEach(benfeitoria.edificacao.documentacao, function (documento) {
                                if (documento.arquivo != null && documento.arquivo != undefined) {
                                    $scope.showEdificacaoBenfeitoria = true;
                                }
                            });
                        }
                    });
                }
            }


            function apresentaDocumentacaoEdificacao() {
                if (imovel.unidadeAutonoma != null && imovel.unidadeAutonoma != undefined && imovel.unidadeAutonoma.edificacao != null) {
                    angular.forEach(imovel.unidadeAutonoma.edificacao.documentacao, function (value) {
                        if (value.arquivo != null && value.arquivo != undefined) {
                            $scope.showEdificacaoAutonoma = true;
                        }
                    });
                }
            }

            function apresentaPlantaEdificacao() {
                if (imovel.unidadeAutonoma != null && imovel.unidadeAutonoma != undefined && imovel.unidadeAutonoma.edificacao != null) {
                    angular.forEach(imovel.unidadeAutonoma.edificacao.plantas, function (value) {
                        if (value.arquivo != null && value.arquivo != undefined) {
                            $scope.showPlantaAutonoma = true;
                        }
                    });
                }
            }

            function apresentaPlantasBenfeitoria() {
                if (imovel.benfeitorias != null && imovel.benfeitorias != undefined) {
                    angular.forEach(imovel.benfeitorias, function (benfeitoria) {
                        if (benfeitoria.plantas != null && benfeitoria.plantas != undefined) {
                            angular.forEach(benfeitoria.plantas, function (planta) {
                                if (planta.planta != null && planta.planta != undefined) {
                                    $scope.showPlantaBenfeitoria = true;
                                }
                            });
                        }
                    });
                }
            }

            $scope.confirmar = function () {
                $scope.envioSei.imovel = $scope.imovel;
                homologarEnviarDestinacaoService.documentoSei($scope.envioSei).then(function () {
                    $mdDialog.cancel();
                    MensagensService.exibirMensagemSucesso("Arquivos enviados para o SEI com sucesso");
                    $timeout(function () {
                        $state.go('INCORPORACAO.detalharImovel', { id: $scope.imovel.id });
                    }, 1000);
                }, function (erro) {
                    MensagensService.exibirMensagemErro(erro);
                });
            };
        }

        self.isTipoTitularAutarquiaFundacao = function () {
            if (
                $scope.imovel.proprietarioTitular &&
                $scope.imovel.proprietarioTitular.tipoProprietarioReal
            ) {
                return (
                    $scope.imovel.proprietarioTitular.tipoProprietarioReal.id ===
                    ID_AUTARQUIAFUNDACAO_REAL
                );
            }
        };

        self.isTipoTitularUniao = function () {
            if (
                $scope.imovel.proprietarioTitular &&
                $scope.imovel.proprietarioTitular.tipoProprietarioReal
            ) {
                return (
                    $scope.imovel.proprietarioTitular.tipoProprietarioReal.id ===
                    ID_UNIAO_PROPRIETARIO_REAL
                );
            }
        };

        self.isAquisicao = function () {
            var isAquisicao;
            if ($scope.imovel.incorporacao.tipoIncorporacao) {
                isAquisicao =
                    $scope.imovel.incorporacao.tipoIncorporacao.id === ID_POR_AQUISICAO;
            }
            return isAquisicao;
        };

        function isOriginalmenteUniao() {
            return $scope.imovel.incorporacao.tipoIncorporacao.id === ID_FORMA_INC_ORIGINALMENTE_DA_UNIAO;
        }

        function isProcessoIncorporacao() {
            return $scope.imovel.incorporacao.situacaoIncorporacao.id === ID_SITUACAO_INCORPORACAO_EMPROCESSO;
        }

        function isImoveisDeTerceiros() {
            return $scope.imovel.incorporacao.tipoIncorporacao.id === ID_IMOVEL_DE_TERCEIROS;
        }

        self.isAquisicaoPorSucessaoPorEntidadesdaAPF = function () {
            var isAquisicaoSucessaoPorEntidadesAPF;
            if ($scope.imovel.incorporacao.tipoAquisicao) {
                isAquisicaoSucessaoPorEntidadesAPF =
                    $scope.imovel.incorporacao.tipoAquisicao.id ===
                    ID_TIPO_AQUISICAO_SUCESSAO_POR_ENTIDADES_APF;
                if (isAquisicaoSucessaoPorEntidadesAPF && $scope.imovel.proprietarioAnterior) {
                    $scope.imovel.proprietarioAnterior.tipoProprietarioReal = undefined;
                }
            }
            return isAquisicaoSucessaoPorEntidadesAPF;
        };

        self.isTipoProprietarioAnteriorAutarquiaFundacao = function () {
            if (
                $scope.imovel.proprietarioAnterior &&
                $scope.imovel.proprietarioAnterior.tipoProprietarioReal
            ) {
                return (
                    $scope.imovel.proprietarioAnterior.tipoProprietarioReal.id ===
                    ID_AUTARQUIAFUNDACAO_REAL
                );
            }
        };

        self.terceiroNaoPossuiCpfOuCnpj = function () {
            if ($scope.imovel.proprietario) {
                return $scope.imovel.proprietario.naoPossuiCpfOuCnpj;
            }
        };

        self.mostraOpcaoTerceiroNaoPossuiCpfOuCnpj = function () {
            var idTipoAquisicao;
            if ($scope.imovel.incorporacao.tipoAquisicao) {
                idTipoAquisicao = $scope.imovel.incorporacao.tipoAquisicao.id;
            }
            return (
                (idTipoAquisicao === ID_TIPO_AQUISICAO_DESAPROPRIACAO_JUDICIAL ||
                    idTipoAquisicao === ID_TIPO_AQUISICAO_ARREMATACAO_JUDICIAL ||
                    idTipoAquisicao === ID_TIPO_AQUISICAO_HERANCA_JACENTE ||
                    idTipoAquisicao === ID_TIPO_AQUISICAO_REGISTRO_POR_APOSSAMENTO) &&
                $scope.imovel.proprietarioAnterior.tipoProprietarioReal.id
            );
        };

        $scope.verificaObrigatoriosProprietario = function () {

            var limpaOrgaoEntidadeProprietarioReal,
                limpaParcialmenteIntegralmenteProprietarioReal,
                limpaProprietarioParcial,
                limpaOrgaoEntidadeProprietarioTitular,
                limpaCpfCnpjDoOrgao,
                limpaProprietarioAnterior,
                limpaTipoProprietarioAnterior,
                limpaOrgaoEntidadeProprietarioAnterior,
                limpaEntidadeExtintaProprietarioAnterior,
                limpaTerceirosProprietarioAnterior,
                limpaJustificativaProprietarioAnterior,
                limpaCheckNaoPossuiCpfCnpj;

            if ($scope.imovel.proprietario.tipoProprietarioReal) {
                limpaOrgaoEntidadeProprietarioReal = !(
                    $scope.imovel.proprietario.tipoProprietarioReal.id ===
                    ID_AUTARQUIAFUNDACAO_REAL
                );
                if (limpaOrgaoEntidadeProprietarioReal) {
                    $scope.imovel.proprietario.codigoUg = undefined;
                    $scope.imovel.proprietario.unidadeUsuario = undefined;
                    $scope.imovel.proprietario.codigoOrgaoEntidade = undefined;
                    $scope.imovel.proprietario.orgaoEntidade = undefined;
                }
            }

            limpaParcialmenteIntegralmenteProprietarioReal = !(!(
                $scope.imovel.incorporacao.tipoIncorporacao.id ===
                ID_IMOVEL_DE_TERCEIROS
            ) && $scope.imovel.proprietario.tipoProprietarioReal.id);

            if (limpaParcialmenteIntegralmenteProprietarioReal) {
                $scope.imovel.proprietario.isIntegralmente = undefined;
            }

            limpaProprietarioParcial =
                $scope.imovel.proprietario.isIntegralmente == 'true';

            if (limpaProprietarioParcial) {
                $scope.imovel.proprietario.tipoProprietarioParcial = undefined;
            }

            if ($scope.imovel.proprietario.tipoProprietarioReal) {
                if (
                    limpaProprietarioParcial &&
                    $scope.imovel.proprietario.tipoProprietarioReal.id !=
                    ID_AUTARQUIAFUNDACAO_REAL
                ) {
                    $scope.imovel.proprietario.codigoOrgaoEntidade = undefined;
                    $scope.imovel.proprietario.orgaoEntidade = undefined;
                }
            }

            limpaOrgaoEntidadeProprietarioTitular = !self.isTipoTitularAutarquiaFundacao();

            if (limpaOrgaoEntidadeProprietarioTitular) {
                $scope.imovel.proprietarioTitular.codigoOrgaoEntidade = undefined;
                $scope.imovel.proprietarioTitular.orgaoEntidade = undefined;
            }

            limpaCpfCnpjDoOrgao = !self.isTipoTitularUniao();

            if (limpaCpfCnpjDoOrgao) {
                $scope.imovel.proprietarioTitular.cnpjAdministracaoIndireta = undefined;
            }

            limpaProprietarioAnterior = !self.isAquisicao();

            if (limpaProprietarioAnterior) {
                $scope.imovel.proprietarioAnterior = undefined;
            }

            limpaTipoProprietarioAnterior = self.isAquisicaoPorSucessaoPorEntidadesdaAPF();
            if ($scope.imovel.proprietarioAnterior) {
                if (limpaTipoProprietarioAnterior) {
                    $scope.imovel.proprietarioAnterior.tipoProprietarioReal = undefined;
                }

                limpaOrgaoEntidadeProprietarioAnterior = !self.isTipoProprietarioAnteriorAutarquiaFundacao();

                if (limpaOrgaoEntidadeProprietarioAnterior) {
                    $scope.imovel.proprietarioAnterior.codigoOrgaoEntidade = undefined;
                    $scope.imovel.proprietarioAnterior.orgaoEntidade = undefined;
                }

                limpaEntidadeExtintaProprietarioAnterior = !self.isAquisicaoPorSucessaoPorEntidadesdaAPF();

                if (limpaEntidadeExtintaProprietarioAnterior) {
                    $scope.imovel.proprietarioAnterior.entidadeExtinta = undefined;
                }

                limpaTerceirosProprietarioAnterior = self.terceiroNaoPossuiCpfOuCnpj();

                if (limpaTerceirosProprietarioAnterior) {
                    $scope.imovel.proprietarioAnterior.pessoas = [];
                }

                limpaJustificativaProprietarioAnterior = !limpaTerceirosProprietarioAnterior;

                if (limpaJustificativaProprietarioAnterior) {
                    $scope.imovel.proprietarioAnterior.justificativa = undefined;
                }

                limpaCheckNaoPossuiCpfCnpj = !self.mostraOpcaoTerceiroNaoPossuiCpfOuCnpj();
                if (limpaCheckNaoPossuiCpfCnpj) {
                    $scope.imovel.proprietarioAnterior.naoPossuiCpfOuCnpj = undefined;
                }
            }

            angular.forEach($scope.forms.proprietarioForm.$error.required, function (
                value,
                key
            ) {
                if (value.$name && value.$name != '') {
                    $scope.forms.proprietarioForm.$setSubmitted();
                    eval.errorsForms.push(value.$name);
                }
            });
        };

        // verificaObrigatoriosLocalizacao
        $scope.verificaObrigatoriosLocalizacao = function () {
            angular.forEach($scope.forms.localizacaoForm.$error.required, function (
                value,
                key
            ) {
                if (value.$name && value.$name != '') {
                    eval.errorsForms.push(value.$name);
                }
            });
            if ($scope.imovel.pontoImovel == undefined) {
                eval.errorsForms.push('pontoImovel');
            }
        };

        $scope.verificaObrigatoriosImovel = function () {
            $scope.verificaObrigatoriosLocalizacao();
            angular.forEach($scope.forms.imovelForm.$error.required, function (
                value,
                key
            ) {
                if (value.$name && value.$name != '') {
                    eval.errorsForms.push(value.$name);
                }
            });
            angular.forEach($scope.forms.imovelForm.$error.minlength, function (
                value,
                key
            ) {
                if (value.$name && value.$name != '') {
                    eval.errorsForms.push(value.$name);
                }
            });
            if ($scope.imovel.pontoImovel == undefined) {
                eval.errorsForms.push('pontoImovel');
            }
        };

        $scope.verificaObrigatoriosTerreno = function () {
            $scope.verificaObrigatoriosImovel();
            angular.forEach($scope.forms.terrenoForm.$error.required, function (
                value,
                key
            ) {
                if (value.$name && value.$name != '') {
                    eval.errorsForms.push(value.$name);
                }
            });
            if (
                $scope.imovel.terreno.geometriaTerreno == undefined &&
                $scope.imovel.terreno.geometriaTerrenoCartorial == undefined
            ) {
                eval.errorsForms.push('geometriaTerreno');
            }
        };

        $scope.verificaObrigatoriosBenfeitora = function () {
            $scope.verificaObrigatoriosImovel();
            $scope.verificaObrigatoriosTerreno();
            angular.forEach(
                $scope.forms.adicionarBenfeitoriaForm.$error.required,
                function (value, key) {
                    if (value.$name && value.$name != '') {
                        eval.errorsForms.push(value.$name);
                    }
                }
            );
            angular.forEach($scope.forms.benfeitoriaForm.$error.required, function (
                value,
                key
            ) {
                if (value.$name && value.$name != '') {
                    eval.errorsForms.push(value.$name);
                }
            });
        };

        $scope.verificaObrigatoriosUnidadeAutonoma = function () {
            $scope.verificaObrigatoriosImovel();
            $scope.verificaObrigatoriosTerreno();
            angular.forEach(
                $scope.forms.adicionarUnidadeAutonomaForm.$error.required,
                function (value, key) {
                    if (value.$name && value.$name != '') {
                        eval.errorsForms.push(value.$name);
                    }
                }
            );

            if (typeof $scope.imovel.unidadeAutonoma === 'undefined') {
                eval.errorsForms.push('geometriaUnidadeAutonoma');
                self.unidadeAutonomaSubmitted = true;
            } else if (
                $scope.imovel.unidadeAutonoma.geometriaUnidadeAutonoma == undefined
            ) {
                eval.errorsForms.push('geometriaUnidadeAutonoma');
                self.unidadeAutonomaSubmitted = true;
            }
        };

        var validacaoController = validacaoController;

        validacaoController.$inject = [
            '$scope',
            '$http',
            '$mdDialog',
            'form',
            'tab',
            'errosForms',
            'erroDesenho',
            'verificaObrigatorios',
        ];

        function validacaoController(
            $scope,
            $http,
            $mdDialog,
            form,
            tab,
            errosForms,
            erroDesenho,
            verificaObrigatorios
        ) {
            $scope.fechar = function () {
                $mdDialog.cancel();
            };

            $scope.errorsForms = [];
            $scope.errorsForms = erroDesenho;
            var imoveisObrigatorios = verificaObrigatorios.getImovel();

            for (var j = 0; j < errosForms.length; j++) {
                for (var i = 0; i < imoveisObrigatorios.length; i++) {
                    if (errosForms[j] == imoveisObrigatorios[i].key) {
                        $scope.errorsForms.push(imoveisObrigatorios[i].nome);
                    } else if (i - 1 == imoveisObrigatorios.length) {
                        $scope.errorsForms.push(imoveisObrigatorios[i].key);
                    }
                }
            }
        }
        /*UPLOAD CONTRATO*/
        self.arquivo = {};
        self.slickConfig1Loaded = true;

        self.removerArquivo = function (index) {
            var confirm = $mdDialog
                .confirm({ focusOnOpen: false })
                .textContent('Deseja confirmar a exclusão do arquivo?')
                .ariaLabel('Excluir Arquivo')
                .targetEvent(index)
                .cancel('Não')
                .ok('Sim');

            $mdDialog.show(confirm).then(
                function () {
                    self.arquivo = null;
                    MensagensService.exibirMensagemSucesso(
                        'Arquivo excluído com sucesso.'
                    );
                    $mdDialog.hide();
                },
                function () {
                    $mdDialog.cancel();
                }
            );
        };

        self.showUploadContratoDialog = function (ev) {
            $mdDialog
                .show({
                    controller: 'ArquivoController',
                    controllerAs: 'arquivoController',
                    templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-upload-arquivo.html',
                    parent: angular.element(document.body),
                    targetEvent: ev,
                    clickOutsideToClose: true,
                    locals: {
                        arquivo: self.arquivo,
                        verificaObrigatorios: verificaObrigatorios,
                    },
                })
                .then(function (arquivo) {
                    self.imovel.arquivo = arquivo;
                });
        };

        /*UPLOAD IMAGENS*/
        self.imagem = {};
        self.imagensTmp = [];
        self.slides = [];
        self.slickConfig1Loaded = true;
        self.imovel.imagens = [];

        self.removerImagem = function (index) {
            var confirm = $mdDialog
                .confirm({ focusOnOpen: false })
                .textContent('Deseja confirmar a exclusão da foto?')
                .ariaLabel('Excluir Imagem')
                .targetEvent(index)
                .cancel('Não')
                .ok('Sim');

            $mdDialog.show(confirm).then(
                function () {
                    self.imovel.imagens.splice(index, 1);
                    self.imagensTmp.splice(index, 1);
                    self.slides.splice(index, 1);
                    MensagensService.exibirMensagemSucesso(
                        'Imagem excluída com sucesso.'
                    );
                    $mdDialog.hide();
                },
                function () {
                    $mdDialog.cancel();
                }
            );
        };

        self.showUploadImagemDialog = function (ev) {
            $mdDialog.show({
                controller: uploadImagemDialogController,
                controllerAs: 'uploadImagemDialogCtrl',
                templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/album-fotos/dialog-upload-fotos.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose: true,
                locals: {
                    imagem: self.imagem,
                    imagensTmp: self.imagensTmp,
                    imagens: self.imovel.imagens,
                    slides: self.slides,
                    verificaObrigatorios: verificaObrigatorios,
                },
            });
        };

        var uploadImagemDialogController = uploadImagemDialogController;

        uploadImagemDialogController.$inject = [
            '$scope',
            '$http',
            '$mdDialog',
            'imagem',
            'imagensTmp',
            'imagens',
            '$sce',
            'ImagemUpload',
            'slides',
            'verificaObrigatorios',
        ];

        function uploadImagemDialogController(
            $scope,
            $http,
            $mdDialog,
            imagem,
            imagensTmp,
            imagens,
            $sce,
            ImagemUpload,
            slides,
            verificaObrigatorios
        ) {
            $scope.imagem = imagem;
            $scope.imagensTmp = imagensTmp;
            $scope.imagens = imagens;
            $scope.slides = slides;
            $scope.imagem = {};
            $scope.errorsForms = [];
            $scope.submitted = false;
            var self = this;
            self.hoje = new Date();

            $scope.fechar = function () {
                $mdDialog.cancel();
            };

            $scope.isCamposValido = function () {
                $scope.errorsForms = verificaObrigatorios.getValoresByForm(
                    $scope.uploadImagem,
                    'imovel'
                );
                if (
                    $scope.uploadImagem.fotoImagem.$error &&
                    $scope.uploadImagem.fotoImagem.$error.maxSize
                ) {
                    $scope.errorsForms.push(
                        'O arquivo da foto não pode ser superior a 5MB.'
                    );
                }
                if ($scope.errorsForms.length == 0) {
                    return true;
                }
                return false;
            };

            $scope.incluir = function () {
                $scope.submitted = true;
                if (
                    validarExtensaoArquivo($scope.imagem.imagem) & $scope.isCamposValido()
                ) {
                    $scope.imagensTmp.push($scope.imagem);
                    salvarArquivo();
                    $scope.imagem = {};
                    self.imagem = {};
                    $mdDialog.hide();
                }
            };

            var salvarArquivo = function () {
                ImagemUpload.save(
                    $scope.imagem,
                    function (response) {
                        $scope.imagens.push(response);
                        renderizarImagemNaTela(response);
                    },
                    function (error) {
                        MensagensService.exibirMensagemErro(error.data.message);
                    }
                );
            };

            var renderizarImagemNaTela = function (response) {
                self.slickConfig1Loaded = false;
                for (var i = 0; i < $scope.imagensTmp.length; i++) {
                    if ($scope.imagensTmp[i].imagem.name == response.nomeImagem) {
                        var reader = new FileReader();

                        reader.onload = function (e) {
                            var url = e.target.result;
                            $scope.slides.push(url);
                            $timeout(function () {
                                self.slickConfig1Loaded = true;
                            }, 2000);
                        };
                        reader.readAsDataURL($scope.imagensTmp[i].imagem);
                        break;
                    }
                }
            };

            var validarExtensaoArquivo = function (arquivo) {
                if (arquivo == undefined) return false;
                else if (getExtensao(arquivo.name) == '.bmp') return true;
                else if (getExtensao(arquivo.name) == '.png') return true;
                else if (
                    getExtensao(arquivo.name) == '.jpg' ||
                    getExtensao(arquivo.name) == '.jpeg'
                )
                    return true;
                else {
                    MensagensService.exibirMensagemErro(
                        'Formato de arquivo inválido. São permitidos apenas arquivos do tipo .BMP, JPG ou PNG.'
                    );
                    return false;
                }
            };

            var getExtensao = function (nomeArquivo) {
                if (nomeArquivo.endsWith('.jpeg')) {
                    return nomeArquivo.substring(nomeArquivo.length - 5);
                }
                return nomeArquivo.substring(nomeArquivo.length - 4);
            };
        }

        var uploadArquivoPlantaBenfeitoriaController = uploadArquivoPlantaBenfeitoriaController;

        uploadArquivoPlantaBenfeitoriaController.$inject = [
            '$scope',
            '$http',
            '$mdDialog',
        ];

        function uploadArquivoPlantaBenfeitoriaController(
            $scope,
            $http,
            $mdDialog
        ) {
            $scope.plantasBenfeitoria = {};

            $scope.fechar = function () {
                $mdDialog.cancel();
            };

            $scope.incluirPlantas = function () {
                self.plantas.push($scope.plantasBenfeitoria);

                fechar();
            };
        }

        /*Modal do Carousel Imagem*/

        self.showModalImagemDialog = function (ev, index) {
            var imagens = [];
            angular.forEach($scope.imovel.imagens, function (value, key) {
                imagens.push($scope.imovel.imagens[key].imagem);
            });
            $mdDialog.show({
                controller: ["$scope", "slides", "startIndex", function ($scope, slides, startIndex) {
                    $scope.slides = slides;
                    $scope.startIndex = startIndex ? startIndex : 0;
                    $scope.fechar = function () {
                        $mdDialog.cancel();
                    };
                }],
                controllerAs: 'showImagemModalCtrl',
                templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos-detalhar-modal.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose: true,
                locals: {
                    slides: imagens,
                    startIndex: index,
                },
            });
        };

        var showImagemModalController = showImagemModalController;

        showImagemModalController.$inject = [
            '$scope',
            '$http',
            '$mdDialog',
            '$sce',
            'slides',
        ];

        function showImagemModalController($scope, $http, $mdDialog, $sce, slides) {
            $scope.slides = slides;

            $scope.fechar = function () {
                $mdDialog.cancel();
            };
        }
        self.imagemBenfeitoria = {};
        self.imagensBenfeitoriaTmp = [];
        self.slidesBenfeitoria = [];
        self.slickConfig2Loaded = true;

        self.benfeitoria.imagens = [];

        self.validarRipFusao = function () {
            var ripImovelRemembrado = $scope.imovel.incorporacao.ripImovelRemembrado;
            if (ripImovelRemembrado) {
                if (ripImovelRemembrado.length === TAMANHO_RIP_VALIDO) {
                    ValidaRipFusaoService.get({ rip: ripImovelRemembrado }).$promise.then(
                        function (response) {
                            if (!response.isValid) {
                                MensagensService.exibirMensagemErro('RIP de Origem não está apto para essa operação');
                                $scope.imovel.incorporacao.ripImovelRemembrado = undefined;
                            } else {
                                self.imovelRemembramento = response.imovel;
                                $scope.imovel.proprietario = response.imovel.proprietario;
                                $scope.imovel.proprietario.isIntegralmente = response.imovel
                                    .proprietario.isIntegralmente ?
                                    'true' :
                                    'false';
                                NotificadorService.notify(
                                    $scope, {
                                    ugProprietario: self.ugProprietario,
                                    orgaoProprietario: self.orgaoEntidadeProprietario,
                                },
                                    NOTIFICADORES.SETAR_PROPRIETARIO_FUSAO
                                );
                            }
                        }
                    );
                } else {
                    self.imovelRemembramento = undefined;
                }
            } else {
                $scope.imovel.incorporacao.ripImovelRemembrado = undefined;
                self.imovelRemembramento = undefined;
            }
        };

        self.detalharImovel = false;

        self.limpaDadosRip = function () {
            $scope.idImovelOrigem = undefined;
            $scope.imovel.incorporacao.codigoOrigem = undefined;
            $scope.imovel.proprietarioAnterior = undefined;
            $scope.imovel.proprietario = undefined;
            $scope.imovel.proprietarioTitular = undefined;
            self.orgaoEntidadeProprietarioParcial = undefined;
            self.ugProprietarioParcial = undefined;
            self.orgaoEntidadeTitular = undefined;
            self.ugProprietarioTitular = undefined;
            self.orgaoEntidadeProprietario = undefined;
            self.ugProprietario = undefined;
            self.ugProprietarioAnterior = undefined;
            self.orgaoEntidadeAnterior = undefined;
            NotificadorService.notify($scope, null, NOTIFICADORES.LIMPAR_UGS_ORGAOS);
        };

        self.validarRip = function () {
            var rip = $scope.imovel.incorporacao.codigoOrigem;
            if (rip) {
                if (rip.length === TAMANHO_RIP_VALIDO) {
                    ValidaRipFracionamentoService.get({ rip: rip }).$promise.then(
                        function (response) {
                            if (!response.isValid) {
                                MensagensService.exibirMensagemErro('RIP de Origem não está apto para essa operação');
                                self.limpaDadosRip();
                            } else {
                                $scope.idImovelOrigem = response.id;
                                if (response.imovel.proprietarioAnterior) {
                                    $scope.imovel.proprietarioAnterior =
                                        response.imovel.proprietarioAnterior;
                                    self.orgaoEntidadeAnterior.id = response.imovel
                                        .proprietarioAnterior.codigoOrgaoEntidade ?
                                        response.imovel.proprietarioAnterior.codigoOrgaoEntidade :
                                        undefined;
                                    self.orgaoEntidadeAnterior.nome = response.imovel
                                        .proprietarioAnterior.orgaoEntidade ?
                                        response.imovel.proprietarioAnterior.orgaoEntidade :
                                        undefined;
                                    self.ugProprietarioAnterior.id = response.imovel
                                        .proprietarioAnterior.codigoUg ?
                                        response.imovel.proprietarioAnterior.codigoUg :
                                        undefined;
                                    self.ugProprietarioAnterior.nome = response.imovel
                                        .proprietarioAnterior.unidadeUsuario ?
                                        response.imovel.proprietarioAnterior.unidadeUsuario :
                                        undefined;
                                }
                                $scope.imovel.proprietarioTitular =
                                    response.imovel.proprietarioTitular;
                                self.orgaoEntidadeProprietarioParcial.id = self.orgaoEntidadeProprietario.id = response
                                    .imovel.proprietario.codigoOrgaoEntidade ?
                                    response.imovel.proprietario.codigoOrgaoEntidade :
                                    undefined;
                                self.orgaoEntidadeProprietarioParcial.nome = self.orgaoEntidadeProprietario.nome = response
                                    .imovel.proprietario.orgaoEntidade ?
                                    response.imovel.proprietario.orgaoEntidade :
                                    undefined;
                                self.ugProprietarioParcial.id = self.ugProprietario.id = response
                                    .imovel.proprietario.codigoUg ?
                                    response.imovel.proprietario.codigoUg :
                                    undefined;
                                self.ugProprietarioParcial.nome = self.ugProprietario.nome = response
                                    .imovel.proprietario.unidadeUsuario ?
                                    response.imovel.proprietario.unidadeUsuario :
                                    undefined;
                                self.orgaoEntidadeTitular.id = response.imovel
                                    .proprietarioTitular.codigoOrgaoEntidade ?
                                    response.imovel.proprietarioTitular.codigoOrgaoEntidade :
                                    undefined;
                                self.orgaoEntidadeTitular.nome = response.imovel
                                    .proprietarioTitular.orgaoEntidade ?
                                    response.imovel.proprietarioTitular.orgaoEntidade :
                                    undefined;
                                self.ugProprietarioTitular.id = response.imovel
                                    .proprietarioTitular.codigoUg ?
                                    response.imovel.proprietarioTitular.codigoUg :
                                    undefined;
                                self.ugProprietarioTitular.nome = response.imovel
                                    .proprietarioTitular.unidadeUsuario ?
                                    response.imovel.proprietarioTitular.unidadeUsuario :
                                    undefined;
                                $scope.imovel.proprietario = response.imovel.proprietario;
                                $scope.imovel.proprietario.isIntegralmente = response.imovel
                                    .proprietario.isIntegralmente ?
                                    'true' :
                                    'false';
                                NotificadorService.notify(
                                    $scope, {
                                    ugProprietario: self.ugProprietario,
                                    ugParcial: self.ugProprietarioParcial,
                                    ugTitular: self.ugProprietarioTitular,
                                    ugAnterior: self.ugProprietarioAnterior,
                                    orgaoProprietario: self.orgaoEntidadeProprietario,
                                    orgaoParcial: self.orgaoEntidadeProprietarioParcial,
                                    orgaoTitular: self.orgaoEntidadeTitular,
                                    orgaoAnterior: self.orgaoEntidadeAnterior,
                                },
                                    NOTIFICADORES.SETAR_UGS_ORGAOS
                                );
                            }
                        }
                    );
                } else {
                    self.limpaDadosRip();
                }
            } else {
                self.limpaDadosRip();
            }
        };

        $scope.tmp = {};

        $scope.file2blob = function (tipo, obj) {
            if (validarExtensaoArquivoDocumentacaoUA(obj)) {
                var r = new FileReader();
                r.onload = function () {
                    var doc = {};
                    switch (tipo) {
                        case 'planoProtecaoIncendioFile':
                            doc.nome = 'Plano de proteção contra incêndio';
                            doc.documento = self.imovel.unidadeAutonoma.planoProtecaoIncendio;
                            doc.idTipoDocumento = '1';
                            break;
                        case 'habiteseFile':
                            doc.nome = 'Habite-se';
                            doc.documento = self.imovel.unidadeAutonoma.habitese;
                            doc.idTipoDocumento = '2';
                            break;
                        case 'alvaraConstrucaoFile':
                            doc.nome = 'Alvará de construção/reforma';
                            doc.documento = self.imovel.unidadeAutonoma.alvaraConstrucao;
                            doc.idTipoDocumento = '3';
                            break;
                        case 'laudoPredialFile':
                            doc.nome = 'Laudo de inspeção predial';
                            doc.documento = self.imovel.unidadeAutonoma.laudoPredial;
                            doc.idTipoDocumento = '4';
                            break;
                        case 'outroFile':
                            //Objeto para gravação do banco
                            doc.nome = self.imovel.unidadeAutonoma.outroDescricao;
                            doc.documento = self.imovel.unidadeAutonoma.outro;
                            doc.idTipoDocumento = '5';

                            // Lista da tela
                            var extensao = getExtensaoDocumentacaoUA(
                                self.imovel.unidadeAutonoma.outro.name
                            );

                            self.outrosDocumentos.push({
                                documento: self.imovel.unidadeAutonoma.outro,
                                nomeExtensao: self.imovel.unidadeAutonoma.outroDescricao + extensao,
                                nome: self.imovel.unidadeAutonoma.outroDescricao,
                            });

                            self.imovel.unidadeAutonoma.outroDescricao = undefined;
                            self.imovel.unidadeAutonoma.outro = undefined;
                            break;
                    }

                    Documento.save(
                        doc,
                        function (response) {
                            self.documentos.push(response);
                        },
                        function () {
                            MensagensService.exibirMensagemErro(
                                'Erro ao gravar o documento: ' + doc.nome
                            );
                        }
                    );
                };
                r.readAsBinaryString(obj);
            } else {
                resetUploadsInvalidos(tipo);
            }
        };

        $scope.removerDocumento = function (tipo) {
            var nome = '';

            switch (tipo) {
                case 'planoProtecaoIncendioFile':
                    nome = 'Plano de proteção contra incêndio';
                    self.imovel.unidadeAutonoma.planoProtecaoIncendio = undefined;
                    break;
                case 'habiteseFile':
                    nome = 'Habite-se';
                    self.imovel.unidadeAutonoma.habitese = undefined;
                    break;
                case 'alvaraConstrucaoFile':
                    nome = 'Alvará de construção/reforma';
                    self.imovel.unidadeAutonoma.alvaraConstrucao = undefined;
                    break;
                case 'laudoPredialFile':
                    nome = 'Laudo de inspeção predial';
                    self.imovel.unidadeAutonoma.laudoPredial = undefined;
                    break;
            }

            var listaDocsFiltrados = $filter('filter')(self.documentos, {
                nome: nome,
            });

            if (listaDocsFiltrados.length > 0) {
                var docExcluir = listaDocsFiltrados[0];
                var index = self.documentos.indexOf(docExcluir);

                self.documentos.splice(index, 1);
            }
        };

        $scope.removerOutroDocumento = function (index, nome) {
            var listaDocsFiltrados = $filter('filter')(self.documentos, {
                nome: nome,
            });

            if (listaDocsFiltrados.length > 0) {
                var docExcluir = listaDocsFiltrados[0];
                var index = self.documentos.indexOf(docExcluir);

                self.documentos.splice(index, 1);
            }

            self.outrosDocumentos.splice(index, 1);
        };

        var resetUploadsInvalidos = function (tipo) {
            switch (tipo) {
                case 'planoProtecaoIncendioFile':
                    $scope.imovel.unidadeAutonoma.planoProtecaoIncendio = undefined;
                    break;
                case 'habiteseFile':
                    $scope.imovel.unidadeAutonoma.habitese = undefined;
                    break;
                case 'alvaraConstrucaoFile':
                    $scope.imovel.unidadeAutonoma.alvaraConstrucao = undefined;
                    break;
                case 'laudoPredialFile':
                    $scope.imovel.unidadeAutonoma.laudoPredial = undefined;
                    break;
                case 'outroFile':
                    $scope.imovel.unidadeAutonoma.outro = undefined;
                    break;
            }
        };
        var getExtensaoDocumentacaoUA = function (nomeArquivo) {
            if (nomeArquivo.endsWith('.jpeg')) {
                return nomeArquivo.substring(nomeArquivo.length - 5);
            }
            return nomeArquivo.substring(nomeArquivo.length - 4);
        };

        // INICIO PAGINACAO PARA TABELA Titulo

        self.paginationTituloAquisitivo = {
            query: {
                limit: 5,
                page: 1,
            },
            limitOptions: [5, 10, 15],
            listaResultante: [],
        };

        // FIM PAGINACAO
        self.confirmarExclusaoBenfeitoria = function (benfeitoria, ev) {
            var confirm = $mdDialog
                .confirm({
                    focusOnOpen: false,
                })
                .textContent('Deseja realmente excluir esta acessão/benfeitoria?')
                .targetEvent(ev)
                .cancel('Não')
                .ok('Sim');
            $mdDialog.show(confirm).then(function () {
                excluirBenfeitoria(benfeitoria);
                NotificadorService.notify(
                    $scope,
                    benfeitoria.codigo,
                    notifiers.BENFEITORIA_EXCLUIDA
                );
            });
        };

        function excluirBenfeitoria(benfeitoria) {
            var index = self.imovel.benfeitorias.indexOf(benfeitoria);
            self.imovel.benfeitorias.splice(index, 1);
        }

        //$mdDialog.cancel();
        ciMapaViewFactory.init();
        ciMapaLayersFactory.init(true);


    }
})();

(function () {
'use strict';
angular.module('su-cadastro-imovel')
	.controller("GlobalController", ["tabsIncorporacao", "$scope", function(tabsIncorporacao, $scope){

        var vm = this;
        vm.tabsService = tabsIncorporacao;
        vm.tabs = vm.tabsService.tabs;
        vm.isMapEmpty = true;

        $scope.selectedIndex = vm.tabsService.selectedIndex;
        $scope.selectTab = function(num){
          vm.setSelectedIndex(num);
        };

        vm.setSelectedIndex = function(num){
          vm.tabsService.selectTab(num);
          $scope.selectedIndex = vm.tabsService.selectedIndex;
        };

      $scope.$on('municipioImovelSelecionado', function (event, data) {
        $scope.codigoIbgeMunicipioImovel = data;
      });


	}]);
})();

(function () {
  'use strict';
  EditarImovelController.$inject = ["$mdDialog", "$mdMedia", "$stateParams", "$state", "$scope", "tabsIncorporacao", "Dominio", "UfsService", "ImovelTerrenoService", "TipoIncorporacaoService", "FundamentoService", "SituacaoIncorporacaoService", "DireitoIncorporadoService", "TipoAquisicaoService", "TipoContratoPessoalService", "TipoImovelFactory", "AutarquiasService", "EstataisService", "ExtintasService", "LogradouroService", "TipoFracionamentoService", "TipoUnidadeAutonomaService", "TipoBenfeitoriaService", "TipoEspecializacaoEdificacaoService", "TipoEspecializacaoComplementarService", "MaterialConstrucaoService", "EstadoConservacaoService", "TipoEstruturaService", "PadraoAcessibilidade", "TipoEdificacao", "TipoAcabamento", "MapaService", "PaisesService", "MunicipioService", "CartorioService", "ExibirCertidao", "Imovel", "ectCepService", "TipoEquipamento", "FormaLevantamento", "EspecializacaoEspelhoDaguaService", "$timeout", "imovelResolve", "pendenciaResolve", "CancelamentoService", "action", "ReceitaService", "ExibirPlantaUnidade", "NOTIFICADORES", "TipoTerreno", "NotificadorService", "TIPODESENHO", "UnidadeAutonomaService", "$rootScope", "verificaObrigatorios", "VerificaUtilizacao", "TipoCancelImovelPesquisa", "Cancelamento", "ciMapaLayersFactory", "ciMapaViewFactory", "imovelService", "MAPA_CONSTANTS_INCORPORACAO", "TipoProprietarioReal", "MensagensService", "escopoCompartilhadoService", "camposAjuda", "PaginacaoFactory", "TerrenoService", "HistoricoImovelService", "homologarEnviarDestinacaoService", "VerificarRestricaoService", "VerificarEdicao"];
  angular
    .module('su-cadastro-imovel')
    .controller('EditarImovelController', EditarImovelController);

  function EditarImovelController(
    $mdDialog,
    $mdMedia,
    $stateParams,
    $state,
    $scope,
    tabsIncorporacao,
    Dominio,
    UfsService,
    ImovelTerrenoService,
    TipoIncorporacaoService,
    FundamentoService,
    SituacaoIncorporacaoService,
    DireitoIncorporadoService,
    TipoAquisicaoService,
    TipoContratoPessoalService,
    TipoImovelFactory,
    AutarquiasService,
    EstataisService,
    ExtintasService,
    LogradouroService,
    TipoFracionamentoService,
    TipoUnidadeAutonomaService,
    TipoBenfeitoriaService,
    TipoEspecializacaoEdificacaoService,
    TipoEspecializacaoComplementarService,
    MaterialConstrucaoService,
    EstadoConservacaoService,
    TipoEstruturaService,
    PadraoAcessibilidade,
    TipoEdificacao,
    TipoAcabamento,
    MapaService,
    PaisesService,
    MunicipioService,
    CartorioService,
    ExibirCertidao,
    Imovel,
    ectCepService,
    TipoEquipamento,
    FormaLevantamento,
    EspecializacaoEspelhoDaguaService,
    $timeout,
    imovelResolve,
    pendenciaResolve,
    CancelamentoService,
    action,
    ReceitaService,
    ExibirPlantaUnidade,
    NOTIFICADORES,
    TipoTerreno,
    NotificadorService,
    TIPODESENHO,
    UnidadeAutonomaService,
    $rootScope,
    verificaObrigatorios,
    VerificaUtilizacao,
    TipoCancelImovelPesquisa,
    Cancelamento,
    ciMapaLayersFactory,
    ciMapaViewFactory,
    imovelService,
    MAPA_CONSTANTS_INCORPORACAO,
    TipoProprietarioReal,
    MensagensService,
    escopoCompartilhadoService,
    camposAjuda,
    PaginacaoFactory,
    TerrenoService,
    HistoricoImovelService,
    homologarEnviarDestinacaoService,
    VerificarRestricaoService,
    VerificarEdicao
  ) {
    var vm = this,
      notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES;
      vm.carregou=false;

    var init = function () {
      vm.temPendenciaSincronizacao();
    };

    $scope.selecionaMunicipio = '';
    $scope.desabilitarUfModal = false;
    vm.isDetail = action == 'DETAIL';
    vm.isEdit = action == 'EDIT';
    vm.paginacaoBenfeitorias = PaginacaoFactory.getDefault();
    vm.tabs = tabsIncorporacao;
    vm.imovel = imovelResolve.imovelAlterado;
    vm.imovelAlterado = {};
    vm.imovelOriginal = imovelResolve.imovelOriginal;
    vm.pendencias = pendenciaResolve;

    carregarUtilizacoes();

    vm.matriculasImovel = vm.imovel.incorporacao.matriculas; //guarda pois está se perdendo

    CancelamentoService.getCancelamentoPorImovel(vm.imovel.id).then(function (
      retorno
    ) {
      var cancelamento = retorno.data;
      if (
        angular.isDefined(cancelamento) &&
        cancelamento !== null &&
        cancelamento !== ''
      ) {
        vm.motivoCancelamento = cancelamento.tipoCancelamentoImovel.descricao;
        if (cancelamento.justificativa != null) {
          vm.motivoCancelamento += ' - ' + cancelamento.justificativa;
        }
      }
    });

    VerificarRestricaoService.get({ rip: vm.imovelOriginal.rip }).$promise.then(
      function (response) {
        if (response.possuiRestricao) {
          vm.temRestricao = true;
        } else {
          vm.temRestricao = false;
        }
      }
    );

    VerificarEdicao.verificar(vm.imovel.rip).then(function success(result) {
      vm.carregou=true;
      if (!result.podeEditar){
        vm.podeEditar = false;
      } else {
        vm.podeEditar = true;
      }
    });

    vm.paises = PaisesService.query();
    vm.ufs = UfsService.UFS;
    vm.tiposIncorporacao = TipoIncorporacaoService.query();
    vm.fundamentos = FundamentoService.query();
    vm.especializacaoEspelhoDagua = EspecializacaoEspelhoDaguaService.query();
    SituacaoIncorporacaoService.query().$promise.then(function (resultado) {
      vm.situacoesIncoporacao = resultado;
    });
    vm.direitosIncorporados = DireitoIncorporadoService.query();
    vm.tiposAquisicao = TipoAquisicaoService.query();
    vm.tiposContratoPessoal = TipoContratoPessoalService.query();
    vm.tiposImoveis = [];
    vm.tiposEntesPublicos = Dominio.tiposEntesPublicos();
    vm.tiposEntesPublicosAnterior = Dominio.tiposEntesPublicosAnterior();
    vm.tiposProprietarioOutorgante = Dominio.tiposProprietarioOutorgante();
    vm.tiposAdministracao = Dominio.tiposAdministracao();
    vm.autarquias = AutarquiasService.query();
    vm.estatais = EstataisService.query();
    vm.extintas;
    vm.tiposLogradouro = LogradouroService.query();
    vm.tiposFracionamento = TipoFracionamentoService.query();
    vm.tiposUnidadesAutonomas = TipoUnidadeAutonomaService.query();
    vm.tiposBenfeitoria = TipoBenfeitoriaService.query();
    vm.tiposEspecializacaoEdificacao =
      TipoEspecializacaoEdificacaoService.query();
    vm.tiposEspecializacaoComplementar =
      TipoEspecializacaoComplementarService.query();
    vm.materiaisConstrucao = MaterialConstrucaoService.query();
    vm.estadosConservacao = EstadoConservacaoService.query();
    vm.tiposEstrutura = TipoEstruturaService.query();
    vm.constantesAcessibilidade = PadraoAcessibilidade.query();
    vm.tiposEdificacao = TipoEdificacao.query();
    vm.tiposAcabamento = TipoAcabamento.query();
    vm.mostraTabTituloAquisitivo = false;
    vm.mostraTabFormDocumento = false;
    vm.mapaService = MapaService;
    vm.arquivoUA = {};

    $scope.imagensImovel = imovelResolve.imovelAlterado.imagens;
    $scope.benfeitoriasCanceladas = [];
    vm.slides = [];
    $scope.forms = {};
    vm.naturezasImoveis = [
      { id: 1, descricao: 'Urbano' },
      { id: 2, descricao: 'Rural' },
      { id: 3, descricao: 'Não se Aplica' }
    ];
    vm.tiposIlhas = [
      { id: 1, descricao: 'Oceânica' },
      { id: 2, descricao: 'Costeira' },
      { id: 3, descricao: 'Fluvial' },
      { id: 4, descricao: 'Lacustre' }
    ];
    var contIdSerializacao = 99;
    vm.pontoNaInsercao = true;
    vm.modelTipoImovel = undefined;
    $scope.errorsForms = [];
    vm.tiposUA = vm.tiposUnidadesAutonomas;
    vm.matricula = {};
    vm.municipios = [];
    vm.cartorios = [];
    $scope.isEditando = true;
    vm.isInclusaoBenfeitoria = false;
    vm.documentoBenfeitoria = {};
    vm.benfeitoria = {
      tipoBenfeitoria: {},
      edificacao: {
        documentacao: [
          {
            nome: 'Plano de Proteção Contra Incêndio',
            possui: 'SEM_INFORMACAO',
            isPrecadastrado: true
          },
          {
            nome: 'Habite-se',
            possui: 'SEM_INFORMACAO',
            isPrecadastrado: true
          },
          {
            nome: 'Alvará de Construção/Reforma',
            possui: 'SEM_INFORMACAO',
            isPrecadastrado: true
          },
          {
            nome: 'Laudo de Inspeção Predial',
            possui: 'SEM_INFORMACAO',
            isPrecadastrado: true
          },
          {
            nome: 'Relatório de Acessibilidade',
            possui: 'SEM_INFORMACAO',
            isPrecadastrado: true
          }
        ],
        dadosMedidor: []
      },
      imagens: [],
      plantas: []
    };
    vm.geometriaTerrenoCadastralValida = true;

    vm.tabs.atualizaTabs(vm.imovel.tipoImovel);

    vm.exibirTabInclusaoBenfeitoria = function (benfeitoria) {
      if (benfeitoria) {
        vm.benfeitoria = angular.copy(benfeitoria);
      }
      vm.isInclusaoBenfeitoria = true;
    };

    vm.tiposProprietarioReal = [];
    TipoProprietarioReal.query().$promise.then(function (result) {
      vm.tiposProprietarioReal = result;
    });
    vm.tiposTitular = [];
    vm.tiposProprietarioAnterior = [];
    vm.orgaosEntidades = [];
    vm.ugsReal = [{ id: 64, nome: 'SENADO FEDERAL' }];
    vm.orgaosEntidades = Dominio.Mocks.orgaosEntidades();
    vm.ugsMinisterio = Dominio.Mocks.ugsMinisterio();
    vm.ugsSenado = Dominio.Mocks.ugsSenado();
    vm.ugsTcu = Dominio.Mocks.ugsTcu();
    TipoProprietarioReal.query().$promise.then(function (result) {
      vm.tiposProprietarioAnterior = angular.copy(result);
      vm.tiposTitular = result;
    });
    vm.tiposProprietarioParcial = [];
    Dominio.tiposProprietarioParcial().then(function (result) {
      vm.tiposProprietarioParcial = result;
    });

    vm.terceiroProprietario = {};
    vm.terceiroProprietarioAnterior = {};

    vm.tipoCadastroEdificacaoUnidadeAutonoma = 'EXISTENTE';
    // Tipos proprietário real
    var ID_AUTARQUIAFUNDACAO_REAL = 5;

    // Formas de incorporação
    var ID_IMOVEL_DE_TERCEIROS = 5,
      ID_POR_FUSAO = 4,
      ID_POR_AQUISICAO = 2,
      ID_FORMA_INC_ORIGINALMENTE_DA_UNIAO = 1;

    //Tipos de Situação de incorporação
    var ID_SITUACAO_INCORPORACAO_INCORPORADO = 1,
      ID_SITUACAO_INCORPORACAO_EMPROCESSO = 2;

    var PERMISSAO_HOMOLOGAR_IMOVEL = 'CAD_IMOVEL.HOMOLOGAR_IMOVEL';

    ciMapaLayersFactory.init(true);
    ciMapaViewFactory.init();

    TipoImovelFactory.buscarTodos().then(function (result) {
      vm.tiposImoveis = result.data;
    });

    $scope.destacaBenfeitoriaMapa = function (codigo) {
      NotificadorService.notify($scope, codigo, notifiers.DESTACAR_BENFEITORIA);
    };

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, dado) {
        vm.imovel.terreno.memorialDescritivoCartorial = dado;
        vm.bloquearMemorialDescritivoCartorial = true;
      },
      NOTIFICADORES.UPLOADSHAPEFILEDESCRICAOCARTORIALEDICAO
    );

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, dado) {
        vm.imovel.terreno.memorialDescritivoCadastral = dado;
        vm.bloquearMemorialDescritivoCadastral = true;
      },
      NOTIFICADORES.UPLOADSHAPEFILEDESCRICAOCADASTRALEDICAO
    );

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, isPontoInsercao) {
        vm.pontoNaInsercao = isPontoInsercao;
      },
      NOTIFICADORES.PONTONAINSERCAOEDICAO
    );

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, data) {
        vm.benfeitoria.geometriaBenfeitoria = data.geoBenfeitoria;
        vm.geometriaValida = data.isValid;
        vm.pontoNaInsercao = true;
      },
      notifiers.GEOMETRIA_BENFEITORIA_ALTERADA
    );

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, tipoDesenhoCadastral) {
        vm.imovel.terreno.tipoDesenhoCadastral = tipoDesenhoCadastral;
      },
      NOTIFICADORES.TIPODESENHOCADASTRAL
    );

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, dado) {
        var tipoTerreno = TipoTerreno.getTipoTerreno();
        if (tipoTerreno === Dominio.Constantes.tipoTerrenoCartorial) {
          vm.imovel.terreno.geometriaTerrenoCartorial = dado;
        } else if (tipoTerreno === Dominio.Constantes.tipoTerrenoCadastral) {
          vm.geometriaterrenoCadastralTemp = dado;
        }
      },
      NOTIFICADORES.UPLOADSHAPEFILESUCCESSEDICAO
    );

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, arquivo) {
        vm.imovel.terreno.arquivoCadastral = arquivo;
      },
      NOTIFICADORES.SHAPEFILECADASTRAL
    );

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, arquivo) {
        vm.imovel.terreno.arquivoCartorial = arquivo;
      },
      NOTIFICADORES.SHAPEFILECARTORIAL
    );

    NotificadorService.subscribe(
      $scope,
      function (evento) {
        delete vm.imovel.terreno.arquivoCadastral;
      },
      NOTIFICADORES.DELETESHAPEFILECADASTRAL
    );

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, data) {
        vm.imovel.terreno.geometriaTerreno = data.geometria;
        vm.geometriaTerrenoCadastralValida = data.isValid;
        vm.pontoNaInsercao = false;
      },
      notifiers.GEOMETRIA_TERRENO_CADASTRAL_ALTERADA
    );

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, isPontoInsercao) {
        vm.pontoNaInsercao = isPontoInsercao;
      },
      notifiers.VERIFICAR_PONTO_NA_ARESTA
    );

    NotificadorService.subscribe(
      $scope,
      function (evento, escopo, data) {
        vm.imovel.unidadeAutonoma.edificacao.geometria = data.geometria;
      },
      notifiers.GEOMETRIA_UNIDADE_AUTONOMA_ALTERADA
    );

    if (vm.imovel.id === vm.imovelOriginal.id) {
      vm.imovel = vm.imovelOriginal;
      vm.cepOriginal = vm.imovel.enderecamento.cep;
    }

    vm.imovel.terreno['@id'] = 99;

    vm.imovel.proprietario.isIntegralmente = vm.imovel.proprietario
      .isIntegralmente
      ? 'true'
      : 'false';
    vm.imovel.incorporacao.dataMatricula
      ? new Date(vm.imovel.incorporacao.dataMatricula)
      : null;
    vm.imovel.incorporacao.dataRegistro
      ? new Date(vm.imovel.incorporacao.dataRegistro)
      : null;

    $scope.imovel = vm.imovel;
    $scope.imovelOriginal = angular.copy(vm.imovelOriginal);
    vm.hoje = new Date();

    escopoCompartilhadoService.setObjetos('hints', camposAjuda);
    extrairBenfeitoriasCanceladas();

    function extrairBenfeitoriasCanceladas() {
      if (vm.imovel.benfeitorias) {
        for (var i = 0; i < vm.imovel.benfeitorias.length; i++) {
          if (!vm.imovel.benfeitorias[i].ativa) {
            $scope.benfeitoriasCanceladas.push(vm.imovel.benfeitorias[i]);
          }
        }
      }
    }

    vm.isImovelSincronizado = function () {
      if (vm.imovel.dsSpiunet) {
        return true;
      }
      return false;
    };

    NotificadorService.subscribe(
      $scope,
      function () {
        carregarUtilizacoes();
      },
      notifiers.CARREGAR_UTILIZACOES_BENFEITORIAS
    );

    function carregarUtilizacoes() {
      vm.utilizacoes = [];
      angular.forEach(vm.imovel.benfeitorias, function (benfeitoria) {
        if (!benfeitoria.ativa) return;
        angular.forEach(benfeitoria.utilizacoes, function (utilizacao) {
          var index = vm.utilizacoes.findIndex(function (u) {
            return u.ripUtilizacao == utilizacao.ripUtilizacao;
            //return u.id === utilizacao.id;
          });
          if (index > -1) {
            vm.utilizacoes[index].benfeitoria += ', ' + benfeitoria.codigo;
          } else {
            utilizacao.benfeitoria = benfeitoria.codigo;
            vm.utilizacoes.push(utilizacao);
          }
        });
      });
      calcularTotaisUtilizacoes();
    }
    vm.setarUtilizacaoNaBenfeitoria = function (item) {
      angular.forEach(vm.imovel.benfeitorias, function (benfeitoria) {
        if (!benfeitoria.ativa) return;
        var index = benfeitoria.utilizacoes.findIndex(function (u) {
          return u.ripUtilizacao == item.ripUtilizacao;
        });
        if (index > -1) {
          benfeitoria.utilizacoes[index] = item;
        }
      });
      calcularTotaisUtilizacoes();
    };
    function calcularTotaisUtilizacoes() {
      vm.totalArea = 0;
      vm.totalFracao = 0;
      vm.totalImovel = 0;
      var utilizacoesJaContabilizadas = [];
      angular.forEach(vm.imovel.benfeitorias, function (benfeitoria) {
        if (!benfeitoria.ativa) return;
        vm.totalImovel += benfeitoria.areaConstruida;
        angular.forEach(benfeitoria.utilizacoes, function (utilizacao) {
          if (!utilizacoesJaContabilizadas.includes(utilizacao.ripUtilizacao)) {
            vm.totalArea += utilizacao.area;
            vm.totalFracao += utilizacao.fracao;
            utilizacoesJaContabilizadas.push(utilizacao.ripUtilizacao);
          }
        });
      });
    }

    MapaService.tipoDesenhoShapefile = TIPODESENHO.SHAPEFILE;
    MapaService.tipoDesenhoVetorizacaoEmTela = TIPODESENHO.VETORIZACAOEMTELA;

    if (vm.imovel.terreno.arquivoCadastral) {
      vm.imovel.terreno.tipoDesenhoCadastral = TIPODESENHO.SHAPEFILE;
    } else {
      vm.imovel.terreno.tipoDesenhoCadastral = TIPODESENHO.VETORIZACAOEMTELA;
    }

    MapaService.tipoDesenho = vm.imovel.terreno.arquivoCadastral;

    vm.imovel.terreno.tipoDesenhoCartorial = 2;

    $scope.incorporacaoNaoNulo = function () {
      return $scope.imovel && $scope.imovel.incorporacao;
    };

    $scope.tipoAquisicaoNaoNulo = function () {
      return (
        $scope.incorporacaoNaoNulo() && $scope.imovel.incorporacao.tipoAquisicao
      );
    };

    $scope.tipoIncorporacaoNaoNulo = function () {
      return (
        $scope.incorporacaoNaoNulo() &&
        $scope.imovel.incorporacao.tipoIncorporacao
      );
    };

    $scope.proprietarioAnteriorNaoNulo = function () {
      return $scope.imovel && $scope.imovel.proprietarioAnterior;
    };

    $scope.proprietarioNaoNulo = function () {
      return $scope.imovel && $scope.imovel.proprietario;
    };

    $scope.tipoProprietarioNaoNulo = function () {
      return (
        $scope.proprietarioNaoNulo() &&
        $scope.imovel.proprietario.tipoProprietarioReal
      );
    };

    $scope.tipoProprietarioParcialNaoNulo = function () {
      return (
        $scope.proprietarioNaoNulo() &&
        $scope.imovel.proprietario.tipoProprietarioParcial
      );
    };

    $scope.$on('removerBenfeitoria', function (ev, data) {
      for (var i = 0; i < vm.imovel.benfeitorias.length; i++) {
        if (vm.imovel.benfeitorias[i].codigo.indexOf(data) != -1) {
          if (vm.imovel.benfeitorias[i].codigo.indexOf('E') != -1) {
            vm.imovel.benfeitorias[i].edificacao.geometriaEdificacao = null;
          } else {
            vm.imovel.benfeitorias[
              i
            ].benfeitoriaComplementar.geometriaBenfeitoriaComplementar = null;
          }
        }
      }
    });

    $scope.$on('pontoAlterado', function (ev, data) {
      vm.imovel.pontoImovel = data;
    });

    $scope.$on('removerUnidadeAutonoma', function (ev, data) {
      vm.imovel.unidadeAutonoma.geometriaUnidadeAutonoma = null;
    });

    $scope.$on('removerTerreno', function (ev, data) {
      if (data) {
        vm.imovel.terreno.geometriaTerreno = null;
        $scope.imovel.terreno.geometriaTerreno = null;
      }
    });

    $scope.showModalValidacaoUnidadeAutonoma = function () {
      eval.errorsForms = [];

      if (
        $scope.imovel.unidadeAutonoma.areaConstruida == null ||
        $scope.imovel.unidadeAutonoma.areaConstruida == undefined
      ) {
        eval.errorsForms.push('Área construída');
      }

      if (
        $scope.imovel.unidadeAutonoma.padraoAcabamento == null ||
        $scope.imovel.unidadeAutonoma.padraoAcabamento == undefined
      ) {
        eval.errorsForms.push('Padrão de Acabamento');
      }

      if (
        $scope.imovel.unidadeAutonoma.estadoConservacao == null ||
        $scope.imovel.unidadeAutonoma.estadoConservacao == undefined
      ) {
        eval.errorsForms.push('Estado de Convervação');
      }

      if (
        $scope.imovel.unidadeAutonoma.tipoGaragemUA == null ||
        $scope.imovel.unidadeAutonoma.tipoGaragemUA == undefined ||
        ($scope.imovel.unidadeAutonoma.tipoGaragemUA == 0 &&
          $scope.imovel.unidadeAutonoma.possuiGaragem)
      ) {
        eval.errorsForms.push('Tipo de Garagem');
      }

      if (
        $scope.imovel.unidadeAutonoma.numeroVagas == null ||
        ($scope.imovel.unidadeAutonoma.numeroVagas == undefined &&
          $scope.imovel.unidadeAutonoma.possuiGaragem)
      ) {
        eval.errorsForms.push('Quantidade Vagas');
      }

      //noinspection JSAnnotator
      $scope.errorsForms = eval.errorsForms;

      //noinspection JSAnnotator
      if (eval.errorsForms.length > 0) {
        $mdDialog.show({
          controllerAs: 'dialogCtrl',
          controller: ["$scope", "$mdDialog", function ($scope, $mdDialog) {
            $scope.fechar = function () {
              $mdDialog.hide();
            };
            //noinspection JSAnnotator
            $scope.errorsForms = eval.errorsForms;
          }],
          preserveScope: true,
          autoWrap: true,
          skipHide: true,
          locals: {
            errorsForms: eval.errorsForms
          },
          templateUrl: ''
        });
      } else {
        vm.salvarImovel();
      }
    };

    $scope.$on('retornoVerificaMapaBing', function (event, args) {
      var anyThing = args.any;

      anyThing.getLayers().forEach(function (lyr) {
        if (lyr.get('name') === 'bingMapa' && lyr.getVisible()) {
          lyr.setVisible(!lyr.getVisible());
          NotificadorService.notify(
            $scope,
            lyr.getVisible(),
            notifiers.ALTERAR_VISIBILIDADE_BING
          );
        }
      });
    });

    $scope.proprietarioIgualAnterior = function () {
      if (
        $scope.imovel.proprietario.tipoProprietarioReal &&
        $scope.imovel.proprietarioAnterior &&
        $scope.imovel.proprietarioAnterior.tipoProprietarioReal &&
        $scope.imovel.proprietario.ugAutarquia &&
        $scope.imovel.proprietario.tipoProprietarioReal.id ===
          $scope.imovel.proprietarioAnterior.tipoProprietarioReal.id
      ) {
        if (
          $scope.imovel.proprietario.ugAutarquia ===
          $scope.imovel.proprietarioAnterior.ugAutarquia
        )
          return true;
      }

      return false;
    };

    $scope.showModalMotivo = function (ev, form, tab, fn) {
      if (
        vm.imovel.incorporacao.tipoIncorporacao.id === ID_IMOVEL_DE_TERCEIROS &&
        vm.imovel.proprietariosTerceiros.length === 0
      ) {
        MensagensService.exibirMensagemCamposObrigatorios(
          'Necessário informar Terceiro.'
        );
        return;
      }

      var outerScope = $scope;
      $mdDialog.show({
        controller: ["$scope", "imovel", "ev", "form", "tab", "fn", "MensagensService", "isValidarCadastroSpiunet", function (
          $scope,
          imovel,
          ev,
          form,
          tab,
          fn,
          MensagensService,
          isValidarCadastroSpiunet
        ) {
          imovel.motivoAlteracao = undefined;
          imovel.cienciaMotivoAlteracao = undefined;
          $scope.imovel = imovel;
          $scope.isValidarCadastroSpiunet = isValidarCadastroSpiunet;

          $scope.confirmar = function () {
            if ($scope.motivoAlteracao.$valid) {
              $scope.fechar();

              outerScope.showModalValidacao(ev, form, tab, fn);
            } else {
              MensagensService.exibirMensagemErro(
                'Necessário preencher o campo motivo e selecionar a declaração de ciência'
              );
            }
          };

          $scope.fechar = function () {
            $mdDialog.cancel();
          };
        }],
        controllerAs: 'showImagemModalCtrl',
        templateUrl:
          'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-motivo-alteracao.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: true,
        locals: {
          imovel: $scope.imovel,
          ev: ev,
          form: form,
          tab: tab,
          fn: fn,
          isValidarCadastroSpiunet: vm.temPendenciaMigracao()
        }
      });
    };

    $scope.showModalValidacao = function (ev, form, tab, fn) {
      $rootScope.$broadcast('broadVerificaMapaBing');
      eval.errorsForms = [];

      if (!$scope.forms.localizacaoForm.$valid) {
        MensagensService.exibirMensagemCamposObrigatorios();
        atualizarAcorddion();
      }

      if (!$scope.forms.proprietarioForm.$valid) {
        $scope.forms.proprietarioForm.$setSubmitted();
        MensagensService.exibirMensagemCamposObrigatorios();
        abrirAcordionProprietario();
      } else if ($scope.proprietarioIgualAnterior()) {
        $scope.imovel.proprietarioAnterior.tipoProprietarioReal = undefined;
        MensagensService.exibirMensagemErro(
          'O proprietário anterior não pode ser o mesmo proprietário do imóvel informado'
        );
        abrirAcordionProprietario();
        return;
      }

      if (!$scope.forms.terrenoForm.$valid) {
        abrirAcordionDescricaoCadastral();
        MensagensService.exibirMensagemCamposObrigatorios();
      }

      if (tab === 2) {
        if (
          !$scope.imovel.terreno.geometriaTerreno &&
          !$scope.imovel.terreno.geometriaTerrenoCartorial
        ) {
          MensagensService.exibirMensagemErro(
            'É necessário informar a geometria do terreno'
          );
          abrirAcordionDescricaoCadastral();
          return;
        }
      }

      var msgPontoForaDoPoligono =
        'O ponto do imóvel não se encontra sobre uma das linhas do vértice do polígono do terreno.';
      var indexTabImovel;
      var indexTabTerreno;
      $scope.submittedLocalizacao = true;
      angular.forEach(tabsIncorporacao.tabs, function (value, key) {
        if (value.nome === 'Imóvel') {
          indexTabImovel = value.index;
        } else if (value.nome === 'Terreno') {
          indexTabTerreno = value.index;
        }
      });

      angular.forEach(
        $scope.forms.imovelForm.$error.required,
        function (value, key) {
          if (value.$name && value.$name !== '') {
            eval.errorsForms.push(value.$name);
          }
        }
      );

      angular.forEach(
        $scope.forms.terrenoForm.$error.required,
        function (value, key) {
          if (value.$name && value.$name !== '') {
            eval.errorsForms.push(value.$name);
          }
        }
      );

      var isSemGeometria = false;
      if (
        vm.isGeometriaTerrenoVazia(vm.imovel.terreno) &&
        ($scope.imovel.terreno.tipoDesenhoCartorial === 2 ||
          $scope.imovel.terreno.tipoDesenhoCadastral === 2)
      ) {
        eval.errorsForms.push('geometriaTerreno');
        isSemGeometria = true;
      }

      if (
        vm.isGeometriaTerrenoVazia(vm.imovel.terreno) &&
        ($scope.imovel.terreno.tipoDesenhoCartorial === 1 ||
          $scope.imovel.terreno.tipoDesenhoCadastral === 1)
      ) {
        eval.errorsForms.push('shapefileNaoCarregado');
        isSemGeometria = true;
      }

      if (vm.imovel.benfeitorias && vm.imovel.benfeitorias.length > 0) {
        vm.pontoNaInsercao = true;
      }

      if (!vm.pontoNaInsercao) {
        MensagensService.exibirMensagemErro(msgPontoForaDoPoligono);
      }

      if (form || isSemGeometria) {
        if (
          vm.imovel.terreno.geometriaTerreno &&
          TipoImovelFactory.isUnidadeAutonoma(vm.imovel.tipoImovel)
        ) {
          if (
            $scope.selectedIndex == 1 &&
            !$scope.forms.terrenoForm.fracaoIdealImovelCadastral.$valid
          ) {
            MensagensService.exibirMensagemErro(
              'O Campo Fração Ideal do Imóvel está inválido'
            );
            abrirAcordionDescricaoCadastral();
          } else {
            $mdDialog.show({
              controller: validacaoController,
              templateUrl:
                'scripts-cadastro-imovel/app/views/dialog-validacao.html',
              parent: angular.element(document.body),
              targetEvent: ev,
              clickOutsideToClose: true,
              locals: {
                form: form,
                tab: tab,
                errosForms: eval.errorsForms,
                erroDesenho: []
              }
            });
          }
        }
      } else if (vm.pontoNaInsercao) {
        if (fn === null) {
          NotificadorService.notify(
            $scope,
            vm.imovel.terreno,
            NOTIFICADORES.TERRENOEDICAO
          );
          vm.mapaService.notificarObservadores(MapaService.IS_MAP_EMPTY, false);
          if (tab === indexTabTerreno) {
            if (
              vm.imovel.terreno.tipoDesenhoCadastral === TIPODESENHO.SHAPEFILE
            ) {
              $('#desenhar-terreno').addClass('disabled');
            } else {
              $('#desenhar-terreno').removeClass('disabled');
            }
          }
          $scope.selectTab(tab);
        } else if (fn === 'validar') {
          $scope.validarDescricaoCartorial(ev);
        } else {
          vm.salvarImovel();
        }
      }
    };

    var validacaoController = function (
      $scope,
      $http,
      $mdDialog,
      form,
      tab,
      errosForms,
      erroDesenho,
      verificaObrigatorios
    ) {
      $scope.fechar = function () {
        $mdDialog.cancel();
      };

      $scope.errorsForms = [];
      $scope.errorsForms = erroDesenho;
      var imoveisObrigatorios = verificaObrigatorios.getImovel();

      for (var j = 0; j < errosForms.length; j++) {
        for (var i = 0; i < imoveisObrigatorios.length; i++) {
          if (errosForms[j] == imoveisObrigatorios[i].key) {
            $scope.errorsForms.push(imoveisObrigatorios[i].nome);
          } else if (i - 1 == imoveisObrigatorios.length) {
            $scope.errorsForms.push(imoveisObrigatorios[i].key);
          }
        }
      }
    };
    validacaoController.$inject = ["$scope", "$http", "$mdDialog", "form", "tab", "errosForms", "erroDesenho", "verificaObrigatorios"];

    vm.secaoAnterior = '';
    vm.validaDadosLocalizacaoImovel = false;
    vm.ultimaSecaoValidada = '';
    vm.secoesExpandidas = [];
    vm.validaSecao = function (ev, form, tab, secaoAtual) {
      vm.imovel.incorporacao.matriculas = vm.matriculasImovel; //Recupera pois está perdendo as matículas por algum motivo
      preparaCartorios();

      var isSecaoAnteriorExpandida = false;
      var isSecaoAtualExpandida = false;
      var secaoAtualIndex = -1;
      var errorsForms = [];

      if (tab === Dominio.Constantes.tabLocalizacaoImovel) {
        vm.validaDadosLocalizacaoImovel = !vm.validaDadosLocalizacaoImovel;
        vm.secaoAnterior = vm.validaDadosLocalizacaoImovel
          ? '.secaoDadosLocalizacaoImovelEditar'
          : '.secaoTipoImovelEditar';
      } else if (tab === Dominio.Constantes.tabDadosGeraisImovel) {
        if (vm.secaoAnterior === '') {
          vm.secaoAnterior = '.secaoDetalhesImovelEditar';
          vm.secoesExpandidas.push('.secaoDetalhesImovelEditar');
        }
      } else if (tab === Dominio.Constantes.tabUnidadeAutonoma) {
        if (vm.secaoAnterior === '') {
          vm.secaoAnterior = '.secaoDadosEdificacaoEditar';
          vm.secoesExpandidas.push('.secaoDadosEdificacaoEditar');
        }
      } else if (tab === Dominio.Constantes.tabBenfeitoria) {
        if (vm.secaoAnterior === '') {
          vm.secaoAnterior = '.secaoDadosAcessaoBenfeitoriaEditar';
          vm.secoesExpandidas.push('.secaoDadosAcessaoBenfeitoriaEditar');
        }
      }

      angular.forEach(vm.secoesExpandidas, function (value, key) {
        if (vm.secaoAnterior === value) {
          isSecaoAnteriorExpandida = true;
        }

        if (secaoAtual === value) {
          isSecaoAtualExpandida = true;
          secaoAtualIndex = key;
        }
      });

      if (isSecaoAtualExpandida) {
        vm.secoesExpandidas.splice(secaoAtualIndex, 1);
      } else {
        vm.secoesExpandidas.push(secaoAtual);
      }

      if (
        (isSecaoAnteriorExpandida &&
          vm.secaoAnterior != vm.ultimaSecaoValidada) ||
        tab === -1
      ) {
        popularErrorsFormsValidacaoSecao(vm.secaoAnterior, form, errorsForms);
        vm.ultimaSecaoValidada = vm.secaoAnterior;
      } else if (
        isSecaoAtualExpandida &&
        secaoAtual !== vm.ultimaSecaoValidada
      ) {
        popularErrorsFormsValidacaoSecao(secaoAtual, form, errorsForms);
        vm.ultimaSecaoValidada = secaoAtual;
      } else {
        vm.secaoAnterior = secaoAtual;
        return;
      }

      vm.secaoAnterior = secaoAtual;

      if (errorsForms.length > 0) {
        showObrigatoriosSecaoModalValidacao(errorsForms, ev, tab, form);
      }
    };

    function popularErrorsFormsValidacaoSecao(secao, form, errorsForms) {
      var elementos = [];
      angular.forEach(angular.element(secao), function (value, key) {
        elementos.push(value.getAttribute('name'));
      });

      if (form && form.$error) {
        angular.forEach(form.$error.required, function (value, key) {
          angular.forEach(elementos, function (valueElementos, keyElementos) {
            if (valueElementos === value.$name) {
              errorsForms.push(valueElementos);
            }
          });
        });
      }

      return errorsForms;
    }

    function showObrigatoriosSecaoModalValidacao(errorsForms, ev, tab, form) {
      $mdDialog.show({
        controller: verificaObrigatorios.getController(),
        templateUrl: 'scripts-cadastro-imovel/app/views/dialog-validacao.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: true,
        locals: {
          form: form,
          tab: tab,
          errorsForms: verificaObrigatorios.getValoresByChaves(errorsForms),
          erroDesenho: vm.mapaService.erroDesenho
        }
      });
    }

    vm.benfeitorias = $scope.imovel.benfeitorias;

    vm.isGeometriaTerrenoVazia = function (terreno) {
      if (terreno == undefined) {
        return true;
      } else if (
        terreno.geometriaTerreno == undefined &&
        terreno.geometriaTerrenoCartorial == undefined
      ) {
        return true;
      }
      return false;
    };

    vm.stringToBool = function (stringWithBooleanValue) {
      return stringWithBooleanValue == 'true' ? true : false;
    };

    vm.salvarImovel = function (ev) {
      limparIdSerializacaoPlantasBenfeitoria();

      $mdDialog.show(
        $mdDialog.alert({
          templateUrl:
            'scripts-cadastro-imovel/pages/imovel/view/modals/progress.tmpl.html'
        })
      );

      if (
        $rootScope.usuarioLogado.jurisdicoes.includes(
          vm.imovel.enderecamento.uf
        )
      ) {
        if (vm.imovel.incorporacao.cartorio != undefined) {
          vm.imovel.incorporacao.cartorioID =
            vm.imovel.incorporacao.cartorio.id;
        }

        /*if (!vm.isAquisicao() && !vm.isTerceiros() && hasTituloAquisitivo()) { // Remover titulos aquisitivos caso não seja De Terceiros ou Por Aquisição
                vm.imovel.titulos = [];
              }*/

        if (vm.imovel.proprietario.codigoOrgaoEntidade) {
          if(typeof(vm.imovel.proprietario.codigoOrgaoEntidade) == 'string'){
            vm.imovel.proprietario.codigoOrgaoEntidade = vm.imovel.proprietario.codigoOrgaoEntidade.replace(/[^a-z0-9]/gi,'');
          }
        }

        if (angular.equals({}, vm.imovel.terreno.arquivoCartorial)) {
          vm.imovel.terreno.arquivoCartorial = undefined;
        }

        if (angular.equals({}, vm.imovel.terreno.arquivoCadastral)) {
          vm.imovel.terreno.arquivoCadastral = undefined;
        }
        if (vm.imovel.unidadeAutonoma != null) {
          vm.imovel.unidadeAutonoma.fracaoIdealCartorial =
            $rootScope.fracaoIdealCartorialNum;
          vm.imovel.unidadeAutonoma.fracaoIdealCadastral =
            $rootScope.fracaoIdealCadastralNum;
        }

        if (
          vm.imovel.incorporacao.matriculas.length == 0 &&
          vm.matriculasImovel.length > 0
        ) {
          //console.log("Recuperando matrículas pois está se perdendo por algum motivo. Info da incorporação:");
          //console.log(vm.imovel.incorporacao);
          vm.imovel.incorporacao.matriculas = vm.matriculasImovel; //Recupera pois está perdendo as matrículas por algum motivo
          preparaCartorios();
        }
        angular.forEach(
          vm.imovel.incorporacao.matriculas,
          function (matricula) {
            if (typeof matricula.uf !== 'string') {
              matricula.uf = matricula.uf.sigla;
            }
          }
        );
        vm.converterUnidadeDeMedida();
        Imovel.save(
          vm.imovel,
          function success(data) {
            $mdDialog.hide();
            MensagensService.exibirMensagemSucesso(
              'O Imóvel foi atualizado com sucesso!'
            );

            if (
              $rootScope.usuarioLogado.permissoes.indexOf(
                'CAD_IMOVEL.HOMOLOGAR_IMOVEL'
              ) !== -1
            ) {
              vm.imovel.numeroProcesso = data.numeroProcesso;
              vm.enviarDocumentosSei();
            } else {
              $timeout(function () {
                $state.go('INCORPORACAO.detalharImovel', { id: vm.imovel.id });
              }, 1000);
            }
          },
          function error(erro) {
            $mdDialog.hide();
            vm.reverterUnidadeDeMedida();
            if (
              erro.data.errors != null &&
              erro.data.errors != undefined &&
              erro.data.errors.length > 0
            ) {
              MensagensService.exibirMensagemErro(
                erro.data.errors[0].defaultMessage
              );
            } else {
              MensagensService.exibirMensagemErro(erro.data.message);
            }
          }
        );
      } else {
        $mdDialog.hide();
        MensagensService.exibirMensagemErro(
          'O imóvel deve estar contido nas jurisdições do usuário.'
        );
      }
    };

    var conversor = 10000;

    vm.converterUnidadeDeMedida = function () {
      if (vm.imovel.terreno.unidadeMedidaCadastral == 'ha') {
        vm.imovel.terreno.areaTotalCadastral =
          vm.imovel.terreno.areaTotalCadastral * conversor;
        vm.imovel.terreno.areaUniaoCadastral =
          vm.imovel.terreno.areaUniaoCadastral * conversor;
        if (
          vm.imovel.terreno.geometriaTerreno &&
          TipoImovelFactory.isUnidadeAutonoma(vm.imovel.tipoImovel)
        ) {
          vm.imovel.unidadeAutonoma.fracaoIdealCadastral =
            vm.imovel.unidadeAutonoma.fracaoIdealCadastral * conversor;
        }
      }
      if (vm.imovel.terreno.unidadeMedidaCartorial == 'ha') {
        vm.imovel.terreno.areaTotalCartorial =
          vm.imovel.terreno.areaTotalCartorial * conversor;
        vm.imovel.terreno.areaUniaoCartorial =
          vm.imovel.terreno.areaUniaoCartorial * conversor;
      }
    };

    //quando dá problema no ponto do imovel
    vm.reverterUnidadeDeMedida = function () {
      if (vm.imovel.terreno.unidadeMedidaCadastral == 'ha') {
        vm.imovel.terreno.areaTotalCadastral =
          vm.imovel.terreno.areaTotalCadastral / conversor;
        vm.imovel.terreno.areaUniaoCadastral =
          vm.imovel.terreno.areaUniaoCadastral / conversor;
        if (
          vm.imovel.terreno.geometriaTerreno &&
          TipoImovelFactory.isUnidadeAutonoma(vm.imovel.tipoImovel)
        ) {
          vm.imovel.unidadeAutonoma.fracaoIdealCadastral =
            vm.imovel.unidadeAutonoma.fracaoIdealCadastral / conversor;
        }
      }
      if (vm.imovel.terreno.unidadeMedidaCartorial == 'ha') {
        vm.imovel.terreno.areaTotalCartorial =
          vm.imovel.terreno.areaTotalCartorial / conversor;
        vm.imovel.terreno.areaUniaoCartorial =
          vm.imovel.terreno.areaUniaoCartorial / conversor;
      }
    };

    function limparIdSerializacaoPlantasBenfeitoria() {
      var benfeitorias = vm.imovel.benfeitorias;
      var plantas = null;
      if (benfeitorias != null && benfeitorias.length > 0) {
        for (var b = 0; b < benfeitorias.length; b++) {
          if (benfeitorias[b].plantas.length > 0) {
            plantas = benfeitorias[b].plantas;
            for (var i = 0; i < plantas.length; i++) {
              plantas[i]['@id'] = contIdSerializacao;
              contIdSerializacao--;
            }
          }
        }
      }
    }

    vm.setModelTipoImovel = function () {
      if (TipoImovelFactory.isUnidadeAutonoma(vm.imovel.tipoImovel)) {
        if (vm.imovel.tipoUnidadeAutonoma) {
          vm.modelTipoImovel = vm.imovel.tipoUnidadeAutonoma;
        }
      } else {
        vm.modelTipoImovel = vm.imovel.tipoImovel;
      }
    };

    vm.setModelTipoImovel();

    if (vm.isDetail) {
      carregarHistoricoPorUUID();
      vm.tabs.addTabHistorico();
    } else {
      vm.tabs.removeTabHistorico();
    }

    $timeout(function () {
      $rootScope.$broadcast('editHideAlterarUnidadeEdificacao');
    }, 3000);

    $scope.abrirPesquisaCartorio = function (ev) {
      var useFullScreen = $mdMedia('sm') || $mdMedia('xs');
      $scope.Dominio = vm.Dominio;
      $mdDialog.show({
        controller: vm.PesquisaCartorioController,
        templateUrl:
          'scripts-cadastro-imovel/pages/imovel/view/pesquisa-cartorio.tmpl.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: false,
        fullscreen: useFullScreen,
        locals: {
          ufSelecionada: $scope.imovel.enderecamento.uf,
          municipioSelecionado: $scope.imovel.enderecamento.municipio
        }
      });
    };

    vm.PesquisaCartorioController = PesquisaCartorioController;

    vm.PesquisaCartorioController.$inject = [
      '$scope',
      '$mdDialog',
      'ufSelecionada',
      'municipioSelecionado'
    ];

    function PesquisaCartorioController(
      $scope,
      $mdDialog,
      ufSelecionada,
      municipioSelecionado
    ) {
      if (ufSelecionada === undefined) {
        $scope.ufs = UfsService.UFS;
        $scope.desabilitarUfModal = false;
      } else {
        $scope.ufs = UfsService.RecuperaUF.query({ sigla: ufSelecionada });
        $scope.municipios = MunicipioService.Municipio.query({
          uf: ufSelecionada,
          municipio: municipioSelecionado
        });
        $scope.desabilitarUfModal = true;
      }

      $scope.hide = function () {
        $mdDialog.hide();
      };
      $scope.cancel = function () {
        $mdDialog.cancel();
      };
      $scope.selecionaCartorio = function (cartorio, cartorioForm, ev) {
        $scope.submittedCartorio = true;
        $scope.todosCamposPreenchidos = true;

        if (vm.imovel.incorporacao == undefined) {
          vm.imovel.incorporacao = {};
        }

        if (
          !$scope.ufSelecionada ||
          !$scope.municipioSelecionado ||
          !$scope.cartorioSelecionado
        ) {
          $scope.todosCamposPreenchidos = false;
        }

        if ($scope.todosCamposPreenchidos) {
          vm.imovel.incorporacao.cartorio = cartorio;
          $mdDialog.hide();
        }
      };
      $scope.pesquisaMunicipio = function (uf) {
        $scope.municipios = $scope.MunicipioService.TodosMunicipios.query({
          uf: uf
        });
      };
      $scope.$watch(
        function (scope) {
          if (ufSelecionada !== undefined) {
            $scope.selecionaUF = 'uf.sigla !== undefined';
          }
          return scope.ufSelecionada;
        },
        function () {
          if (
            $scope.ufSelecionada !== undefined &&
            municipioSelecionado === undefined
          ) {
            $scope.cartorioSelecionado = undefined;
            $scope.municipios = vm.MunicipioService.TodosMunicipios.query({
              uf: $scope.ufSelecionada
            });
          }
        }
      );
      $scope.$watch(
        function (scope) {
          if (municipioSelecionado !== undefined) {
            $scope.selecionaMunicipio =
              'municipio.nome ===' + '\'' + municipioSelecionado + '\'';
          }
          return scope.municipioSelecionado;
        },
        function () {
          if ($scope.municipioSelecionado !== undefined) {
            $scope.cartorioSelecionado = undefined;
            $scope.cartorios = CartorioService.pesquisaCartoriosPorUF.query({
              uf: $scope.ufSelecionada,
              municipio: $scope.municipioSelecionado
            });
          }
        }
      );
    }

    vm.mapaService.registrarObservadorCallback(
      vm.mapaService.PONTO_IMOVEL,
      function (pontoImovel) {
        $scope.hasPontoImovel = true;
        vm.imovel.pontoImovel = pontoImovel;
      }
    );

    var dialogBenfeitoriasCanceladaController = function (
      $scope,
      $http,
      $mdDialog,
      benfeitorias
    ) {
      $scope.benfeitorias = benfeitorias;
      $scope.paginacao = PaginacaoFactory.getDefault();

      $scope.fechar = function () {
        $mdDialog.cancel();
      };
    };
    dialogBenfeitoriasCanceladaController.$inject = ["$scope", "$http", "$mdDialog", "benfeitorias"];

    $scope.modalBenfeitoriasCanceladas = function (ev) {
      $mdDialog.show({
        controller: dialogBenfeitoriasCanceladaController,
        templateUrl:
          'scripts-cadastro-imovel/pages/benfeitoria/view/dialog-benfeitorias-canceladas.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: false,
        locals: {
          benfeitorias: $scope.benfeitoriasCanceladas
        }
      });
    };

    $scope.modalCancelarBenfeitoria = function (
      ev,
      idBenfeitoria,
      indexArrayBenfeitoria
    ) {
      $mdDialog.show({
        controller: cancelarBenfeitoriaController,
        templateUrl:
          'scripts-cadastro-imovel/pages/benfeitoria/view/dialog-cancelar-benfeitoria.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: false,
        locals: {
          id: idBenfeitoria,
          index: indexArrayBenfeitoria,
          benfeitorias: vm.imovel.benfeitorias
        }
      });
    };

    // controle de abas

    //$scope.selectedIndex = vm.tabs.selectedIndex;
    if ($stateParams.tab) {
      vm.tabs.selectedIndex = parseInt($stateParams.tab);
    }

    $scope.selectedIndex = vm.tabs.selectedIndex;
    $scope.selectTab = function (num) {
      vm.setSelectedIndex(num);
    };

    vm.setSelectedIndex = function (num) {
      vm.tabs.selectTab(num);
      $scope.selectedIndex = vm.tabs.selectedIndex;
      $scope.$broadcast('mudouAba', { abaSelecionada: $scope.selectedIndex });
    };

    $scope.downloadArquivoHistorico = function (arquivo, noArquivo, type) {
      if (arquivo && noArquivo) {
        type = type ? type : noArquivo.split('.')[1];

        var a = window.document.createElement('a');
        a.href = arquivo;
        a.href = 'data:application/' + type + ';base64,' + arquivo;
        a.download = noArquivo;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    };

    var cancelarBenfeitoriaController = function (
      $scope,
      $http,
      $mdDialog,
      id,
      index,
      CancelarBenfeitoria,
      benfeitorias
    ) {
      $scope.benfeitoriaCancelada = {};
      $scope.benfeitoriaCancelada.id = id;
      var codigo = '';

      $scope.fechar = function () {
        $mdDialog.cancel();
      };

      $scope.cancelarBenfeitoria = function () {
        CancelarBenfeitoria.save(
          { benfeitoria: angular.toJson($scope.benfeitoriaCancelada) },
          function (response) {
            MensagensService.exibirMensagemSucesso(
              'Benfeitoria cancelada com sucesso.'
            );
            $mdDialog.cancel();
            codigo = benfeitorias[index].codigo;
            benfeitorias[index] = response;
            NotificadorService.notify(
              $scope,
              codigo,
              notifiers.EXCLUI_BENFEITORIA_BY_ID
            );
            carregarUtilizacoes();
          },
          function () {
            MensagensService.exibirMensagemErro(
              'Não foi posível cancelar a benfeitoria selecionada. Entre em contato com o administrador do sistema.'
            );
          }
        );
      };
    };
    cancelarBenfeitoriaController.$inject = ["$scope", "$http", "$mdDialog", "id", "index", "CancelarBenfeitoria", "benfeitorias"];

    $scope.isCepGenerico = false;

    function verificarCepGenerico(stLocalidade) {
      if (stLocalidade == 0) {
        return true;
      }
      return false;
    }

    verificarCepGenerico(vm.imovel.enderecamento.cep);

    vm.cepValido = false;
    vm.buscaEnderecoPorCep = function () {
      var cep = vm.imovel.enderecamento.cep;
      var numero = vm.imovel.numero;
      if (cep != undefined && cep.length == 8) {
        ectCepService.get({ cep: cep }).$promise.then(
          function (data) {
            if (
              data.municipio !== $scope.imovelOriginal.enderecamento.municipio
            ) {
              MensagensService.exibirMensagemErro(
                'Este tipo de alteração não é permitido. Se ela for mesmo necessária, realize o cancelamento e inclua um novo imóvel.'
              );
              vm.imovel.enderecamento.cep = vm.cepOriginal;
            } else {
              data.pais = Dominio.Constantes.Brasil;
              vm.imovel.enderecamento = data;
              vm.imovel.numero = numero;
              $scope.$emit('municipioImovelSelecionado', data.codigoIbge);
              verificarCepGenerico(data.stLocalidade);
              vm.cepValido = true;
            }
          },
          function () {
            MensagensService.exibirMensagemErro('CEP não encontrado');
            vm.cepValido = false;
            vm.imovel.enderecamento = $scope.imovelOriginal.enderecamento;
            //na segunda vez que da erro, o $scope.imovelOriginal.enderecamento.cep fica undefined...
            vm.imovel.enderecamento.cep = vm.cepOriginal;
          }
        );
      } else {
        vm.cepValido = false;
        vm.imovel.enderecamento = {};
        vm.imovel.numero = numero;
        vm.imovel.enderecamento.pais = Dominio.Constantes.Brasil;
      }
    };

    function carregarHistoricoPorUUID() {
      HistoricoImovelService.getHistorico(vm.imovel.uuid).then(
        function (data) {
          vm.listaHistorico = data.data;
        },
        function (error) {
          MensagensService.exibirMensagemErro(
            'Ocorreu um erro ao carregar o histórico do imóvel. ' +
              error.data.message
          );
        }
      );
    }

    vm.recuperarPropriedadesAlteradasHistorico = function (
      historico,
      propriedade
    ) {
      if (historico.setMapaPropriedadesString[propriedade][0].includes('{')) {
        var semChave1 = historico.setMapaPropriedadesString[propriedade][0]
          .replace('{', '')
          .replace('}', '');
        var semChave2 = historico.setMapaPropriedadesString[propriedade][1]
          .replace('{', '')
          .replace('}', '');
        semChave1 = semChave1.split(',');
        semChave2 = semChave2.split(',');
        var result = '';
        for (var i = 0; i <= semChave1.length; i++) {
          if (semChave1[i] != semChave2[i]) {
            result +=
              semChave1[i].split('=').shift() +
              ': ' +
              semChave2[i].split('=').pop() +
              ' | ' +
              semChave1[i].split('=').pop() +
              '; ';
          }
        }
        return result;
      } else if (!historico || !historico.setMapaPropriedadesString) {
        return '';
      } else {
        return (
          historico.setMapaPropriedadesString[propriedade][1] +
          ' | ' +
          historico.setMapaPropriedadesString[propriedade][0]
        );
      }
    };

    vm.recuperarPropriedadesHistorico = function (historico) {
      if (!historico || !historico.setMapaPropriedadesString) {
        return [];
      }
      return Object.keys(historico.setMapaPropriedadesString);
    };

    $scope.validarDescricaoCartorial = function (ev) {
      if (!TipoImovelFactory.isUnidadeAutonoma(vm.imovel.tipoImovel)) {
        var confirm = $mdDialog
          .confirm({
            focusOnOpen: false
          })
          .textContent(
            'Os dados da Descrição Cartorial do Terreno serão perdidos e não poderão ser recuperados. Deseja confirmar?'
          )
          .ariaLabel('confirmacao')
          .targetEvent(ev)
          .ok('Sim')
          .cancel('Não');

        var matricula = $scope.imovel.incorporacao.numeroMatricula;
        var descricaoCartorial =
          $scope.imovel.terreno.memorialDescritivoCartorial;
        if (matricula === null || matricula === '') {
          $mdDialog.show(confirm).then(
            function () {
              descricaoCartorial = '';
              $scope.selectTab(1);
            },
            function () {
              $mdDialog.close();
            }
          );
        } else $scope.selectTab(1);
      } else {
        $scope.selectTab(1);
      }
    };

    // BENFEITORIAS
    $scope.benfeitoriaSelecionada = {};
    var fotosBenfeitoria = [];

    function extrairFotosBenfeitoria(codigo) {
      fotosBenfeitoria = [];
      for (var i = 0; i < vm.imovel.benfeitorias.length; i++) {
        if (vm.imovel.benfeitorias[i].codigo === codigo) {
          fotosBenfeitoria = vm.imovel.benfeitorias[i].imagens;
        }
      }
    }

    $scope.showInserirBenfeitoriaDialog = function (ev, benfeitoria) {
      var el;
      if (benfeitoria === null) {
        el = {};
      } else {
        el = benfeitoria;
      }
      extrairFotosBenfeitoria(el.codigo);
      var dataGeometria = {
        pontoImovel: vm.imovel.pontoImovel,
        geometriaTerreno: vm.imovel.terreno.geometriaTerreno
      };

      $mdDialog.show({
        controller: benfeitoriaController,
        templateUrl:
          'scripts-cadastro-imovel/pages/benfeitoria/view/dialog-adicionar-benfeitoria.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        locals: {
          benfeitoria: el,
          isDetail: vm.isDetail,
          imovel: vm.imovel,
          dataGeometria: dataGeometria,
          fnAdicionarBenfeitoria: vm.adicionarBenfeitoria
        },
        clickOutsideToClose: false
      });

      $timeout(function () {
        if (angular.element(document).find('md-dialog').length > 0) {
          NotificadorService.notify(
            $scope,
            null,
            NOTIFICADORES.INJETARMAPADIALOGADICIONARBENFEITORIA
          );
        }
      }, 2000);
    };

    var benfeitoriaController = function (
      $scope,
      $mdDialog,
      benfeitoria,
      isDetail,
      fnAdicionarBenfeitoria,
      dataGeometria,
      imovel
    ) {
      var vm = this;
      vm.benfeitoria = benfeitoria;
      $scope.benfeitoria = vm.benfeitoria;
      $scope.benfeitorias = vm.benfeitorias;
      $scope.geometrias = dataGeometria;
      $scope.imovel = imovel;
      $scope.isDetail = isDetail;
      $scope.imagensBenfeitoria = fotosBenfeitoria;

      $scope.$on('benfeitoriaIncluida', function (event, data) {
        benfeitoria = data;
        fnAdicionarBenfeitoria(benfeitoria);
      });

      $scope.fechar = function () {
        $mdDialog.cancel();
        vm.benfeitoria = {};
      };
    };
    benfeitoriaController.$inject = ["$scope", "$mdDialog", "benfeitoria", "isDetail", "fnAdicionarBenfeitoria", "dataGeometria", "imovel"];

    $scope.pesquisarBenfeitoria = function (benfeitoria) {
      $scope.benfeitoriaSelecionada = benfeitoria;
    };

    $scope.resetBenfeitoriaSelecionada = function () {
      $scope.benfeitoriaSelecionada = {};
    };

    vm.arrayProprietario = [];
    if (vm.imovel.proprietario && vm.imovel.proprietario.pessoas) {
      vm.arrayProprietario = vm.imovel.proprietario.pessoas;
    }

    vm.pesquisarcpfCnpjPrivado = function (cpfCnpjPrivado) {
      ReceitaService.get({ cpfCnpj: cpfCnpjPrivado }, function (data) {
        if (data.id) {
          vm.verificaIndiceDuplicadoProprietario(vm.arrayProprietario, data);
          vm.imovel.proprietario.pessoas = vm.arrayProprietario;
        } else {
          MensagensService.exibirMensagemErro('CPF/CNPJ não cadastrado');
        }
      });
    };

    vm.removeProprietarioParcialPrivado = function (proprietario) {
      var index = vm.imovel.proprietario.pessoas.indexOf(proprietario);

      var confirm = $mdDialog
        .confirm({ focusOnOpen: false })
        .textContent('Deseja realmente remover este item?')
        .ariaLabel('Excluir Proprietário')
        .targetEvent(index)
        .cancel('Não')
        .ok('Excluir');

      $mdDialog.show(confirm).then(
        function () {
          vm.imovel.proprietario.pessoas.splice(index, 1);
          MensagensService.exibirMensagemSucesso(
            'Proprietário excluído com sucesso.'
          );
          $mdDialog.hide();
        },
        function () {
          $mdDialog.cancel();
        }
      );
    };

    vm.buscaCpfCnpjPrivadoTitular = function (cpfCnpjPrivado) {
      if (cpfCnpjPrivado != undefined && cpfCnpjPrivado.length >= 10) {
        ReceitaService.get({ cpfCnpj: cpfCnpjPrivado }, function (data) {
          $scope.cpfCpnj = data;
        });
      }
    };
    if (vm.imovel.proprietarioAnterior) {
      vm.arrayProprietarioAnterior = vm.imovel.proprietarioAnterior.pessoas;
    }

    vm.pesquisarcpfCnpjPrivadoAnterior = function (cpfCnpjPrivadoAnterior) {
      ReceitaService.get({ cpfCnpj: cpfCnpjPrivadoAnterior }, function (data) {
        if (data.id) {
          vm.verificaIndiceDuplicadoProprietario(
            vm.arrayProprietarioAnterior,
            data
          );
          vm.imovel.proprietarioAnterior.pessoas = vm.arrayProprietarioAnterior;
        } else {
          MensagensService.exibirMensagemErro('CPF/CNPJ não cadastrado');
        }
      });
    };

    vm.removeProprietarioPrivadoAnterior = function (
      proprietarioPrivadoAnterior
    ) {
      var indexProprietarioPrivadoAnterior =
        vm.imovel.proprietarioAnterior.pessoas.indexOf(
          proprietarioPrivadoAnterior
        );

      var confirm = $mdDialog
        .confirm({ focusOnOpen: false })
        .textContent('Deseja realmente remover este item?')
        .ariaLabel('Excluir Proprietário')
        .targetEvent(indexProprietarioPrivadoAnterior)
        .cancel('Não')
        .ok('Excluir');

      $mdDialog.show(confirm).then(
        function () {
          vm.imovel.proprietarioAnterior.pessoas.splice(
            indexProprietarioPrivadoAnterior,
            1
          );
          MensagensService.exibirMensagemSucesso(
            'Proprietário excluído com sucesso.'
          );
          $mdDialog.hide();
        },
        function () {
          $mdDialog.cancel();
        }
      );
    };

    vm.verificaIndiceDuplicadoProprietario = function (
      arrayProprietario,
      objetoProprietario
    ) {
      if (arrayProprietario == 0) {
        arrayProprietario.push(objetoProprietario);
      } else {
        for (var i = 0; i <= arrayProprietario.length; i++) {
          if (
            angular.toJson(arrayProprietario[i]) ==
            angular.toJson(objetoProprietario)
          ) {
            return;
          }
        }
        arrayProprietario.push(objetoProprietario);
      }
    };

    vm.adicionarBenfeitoria = function (obj) {
      vm.benfeitoria = obj;

      $scope.imovel.benfeitorias = $scope.imovel.benfeitorias
        ? $scope.imovel.benfeitorias
        : [];
      if (vm.benfeitoria.codigo) {
        vm.removerBenfeitoria(vm.benfeitoria, false);
      }
      gerarIdBenfeitoria();

      if (vm.benfeitoria.geometriaBenfeitoria) {
        if (vm.benfeitoria.codigo.startsWith('C', 0)) {
          vm.benfeitoria.benfeitoriaComplementar.geometriaBenfeitoriaComplementar =
            vm.benfeitoria.geometriaBenfeitoria;
        } else {
          vm.benfeitoria.edificacao.geometriaEdificacao =
            vm.benfeitoria.geometriaBenfeitoria;
        }
      }

      $scope.imovel.benfeitorias.push(vm.benfeitoria);

      NotificadorService.notify(
        $scope,
        vm.benfeitoria.codigo,
        notifiers.SET_ID_BENFEITORIA
      );
      var data = vm.benfeitorias;
      MensagensService.exibirMensagemSucesso(
        'Benfeitoria incluída com sucesso'
      );
      vm.benfeitoria = {};
    };

    vm.removerBenfeitoria = function (obj, confirmarAntesExcluir) {
      if (confirmarAntesExcluir) {
        var confirm = $mdDialog.confirm({
          focusOnOpen: false,
          textContent: 'Deseja realmente excluir esta acessão/benfeitoria?',
          ok: 'Sim',
          cancel: 'Não'
        });

        $mdDialog.show(confirm).then(function () {
          controleExclusaoBenfeitoria(obj);
          MensagensService.exibirMensagemSucesso(
            'Benfeitoria removida com sucesso'
          );
        });
      } else {
        controleExclusaoBenfeitoria(obj);
        MensagensService.exibirMensagemSucesso(
          'Benfeitoria removida com sucesso'
        );
      }
    };

    function controleExclusaoBenfeitoria(obj) {
      for (var i = 0; i < $scope.imovel.benfeitorias.length; i++) {
        if ($scope.imovel.benfeitorias[i].codigo == obj.codigo) {
          $scope.imovel.benfeitorias.splice(i, 1);
        }
      }
    }

    function gerarIdBenfeitoria() {
      $scope.imovel.benfeitorias = $scope.imovel.benfeitorias
        ? $scope.imovel.benfeitorias
        : [];
      var benf = $scope.imovel.benfeitorias;
      var prefixo = isEdificacao() ? 'E' : 'C';
      var sufixo = 1;
      var array = [];
      for (var i = 0; i < benf.length; i++) {
        if (prefixo == benf[i].codigo.substring(0, 1)) {
          array.push(parseInt(benf[i].codigo.substring(1)));
        }
      }
      while (array.includes(sufixo)) {
        sufixo++;
      }
      var codigo = prefixo + sufixo;
      vm.benfeitoria.codigo = codigo;
      vm.benfeitoria.ativa = true;
    }

    vm.limparBenfeitoria = function () {
      vm.benfeitoria = {};
    };

    function isEdificacao() {
      return vm.benfeitoria.tipoBenfeitoria.id == 1;
    }

    // FIM BENFEITORIAS

    vm.certidaoCartorial = {};
    vm.removerCertidao = function (index) {
      if (confirm('Tem certeza que deseja excluir este arquivo?')) {
        vm.imovel.certidoesCartorial.splice(index, 1);

        MensagensService.exibirMensagemSucesso(
          'Certidão excluída com sucesso.'
        );
      }
    };

    vm.resetarCertidaoCartorial = function () {
      vm.certidaoCartorial = {};
    };

    vm.showUploadCertidaoCartorialDialog = function (ev) {
      $mdDialog.show({
        controller: uploadCertidaoCartorialDialogController,
        templateUrl:
          'scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-upload-certidao-cartorial.html',
        parent: angular.element(document.body),
        targetEvent: ev,

        clickOutsideToClose: false,
        locals: {
          certidaoCartorial: vm.certidaoCartorial,
          certidoesCartorial: vm.imovel.certidoesCartorial,
          dataMatricula: vm.imovel.incorporacao.dataMatricula
        }
      });
    };

    var uploadCertidaoCartorialDialogController = function (
      $scope,
      $http,
      $mdDialog,
      certidaoCartorial,
      certidoesCartorial,
      $sce,
      CertidaoCartorial,
      dataMatricula,
      ExibirCertidao,
      verificaObrigatorios
    ) {
      $scope.certidaoCartorial = certidaoCartorial;
      $scope.certidoesCartorial = certidoesCartorial;

      $scope.fechar = function () {
        $mdDialog.cancel();
      };

      $scope.incluir = function () {
        $scope.certidaoCartorialSubmitted = true;

        if (
          $scope.uploadCertidaoCartorial.$valid &&
          validarDataMatriculaDataEmissao(
            dataMatricula,
            $scope.certidaoCartorial.dataEmissao
          ) &&
          validarExtensaoArquivo($scope.certidaoCartorial.certidao)
        ) {
          if (validarCertidaoUnica($scope.certidaoCartorial)) {
            salvarArquivo($scope.certidaoCartorial);
            $scope.certidaoCartorialSubmitted = false;
          }
        } else {
          $mdDialog.show({
            controllerAs: 'dialogCtrl',
            controller: verificaObrigatorios.getController(),
            preserveScope: true,
            autoWrap: true,
            skipHide: true,
            locals: {
              errorsForms: verificaObrigatorios.getValoresByForm(
                $scope.uploadCertidaoCartorial,
                'imovel'
              )
            },
            templateUrl:
              'scripts-cadastro-imovel/app/views/dialog-validacao.html'
          });
        }
      };

      var validarDataMatriculaDataEmissao = function (
        dataMatricula,
        dataEmissao
      ) {
        var dataM = new Date(dataMatricula);
        var dataE = new Date(dataEmissao);
        if (dataEmissao < dataMatricula) {
          MensagensService.exibirMensagemErro(
            'A Data de Emissão não pode ser anterior a Data da Matrícula/Transcrição informada.'
          );
          return false;
        }
        return true;
      };

      var salvarArquivo = function (certidao) {
        CertidaoCartorial.save(
          certidao,
          function (response) {
            $scope.certidoesCartorial.push(response);
            $scope.certidaoCartorial = {};
          },
          function (error) {
            MensagensService.exibirMensagemErro(error.data.message);
          }
        );
      };

      var validarCertidaoUnica = function () {
        if ($scope.certidoesCartorial.length <= 0) {
          return true;
        } else {
          for (var i = 0; i < $scope.certidoesCartorial.length; i++) {
            if (
              $scope.certidoesCartorial[i].certidao.name ==
              $scope.certidaoCartorial.certidao.name
            ) {
              MensagensService.exibirMensagemErro(
                'O documento selecionado já foi enviado.'
              );
              return false;
            }
          }
          return true;
        }
      };

      $scope.removerCertidao = function (index) {
        if (confirm('Tem certeza que deseja excluir este arquivo?')) {
          $scope.certidoesCartorial.splice(index, 1);
          MensagensService.exibirMensagemSucesso(
            'Certidão excluída com sucesso.'
          );
        }
      };

      var validarExtensaoArquivo = function (arquivo) {
        if (getExtensao(arquivo.name) == '.pdf') return true;
        else if (getExtensao(arquivo.name) == '.png') return true;
        else if (getExtensao(arquivo.name) == '.jpg') return true;
        else {
          MensagensService.exibirMensagemErro(
            'Formato de arquivo inválido. São permitidos apenas arquivos do tipo PDF, JPG ou PNG.'
          );
          return false;
        }
      };

      $scope.hoje = new Date();

      var getExtensao = function (nomeArquivo) {
        return nomeArquivo.substring(nomeArquivo.length - 4);
      };

      $scope.confirmar = function () {
        $mdDialog.hide();
      };

      $scope.visualizarCertidao = function (id) {
        ExibirCertidao.visualizarCertidao(id);
      };
    };
    uploadCertidaoCartorialDialogController.$inject = ["$scope", "$http", "$mdDialog", "certidaoCartorial", "certidoesCartorial", "$sce", "CertidaoCartorial", "dataMatricula", "ExibirCertidao", "verificaObrigatorios"];

    $scope.visualizarCertidao = function (id) {
      ExibirCertidao.visualizarCertidao(id);
    };

    /*UPLOAD CONTRATO*/

    vm.arquivo = {};
    vm.slickConfig1Loaded = true;
    vm.removerArquivo = function (index) {
      var confirm = $mdDialog
        .confirm({ focusOnOpen: false })
        .textContent('Deseja confirmar a exclusão do arquivo?')
        .ariaLabel('Excluir Arquivo')
        .targetEvent(index)
        .cancel('Não')
        .ok('Sim');

      $mdDialog.show(confirm).then(
        function () {
          vm.arquivo = null;
          MensagensService.exibirMensagemSucesso(
            'Arquivo excluído com sucesso.'
          );
          $mdDialog.hide();
        },
        function () {
          $mdDialog.cancel();
        }
      );
    };

    vm.showUploadContratoDialog = function (ev) {
      $mdDialog
        .show({
          controller: 'ArquivoController',
          controllerAs: 'arquivoController',
          templateUrl:
            'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-upload-arquivo.html',
          parent: angular.element(document.body),
          targetEvent: ev,
          clickOutsideToClose: true,
          locals: {
            arquivo: vm.arquivo,
            verificaObrigatorios: verificaObrigatorios
          }
        })
        .then(function (arquivo) {
          vm.imovel.arquivo = arquivo;
        });
    };

    /*UPLOAD IMAGENS*/
    vm.imagem = {};
    vm.imagensTmp = [];
    vm.slides = [];
    vm.slickConfig1Loaded = true;
    vm.imovel.imagens = vm.imovel.imagens || [];

    vm.removerImagem = function (index) {
      var confirm = $mdDialog
        .confirm({ focusOnOpen: false })
        .textContent('Deseja confirmar a exclusão da foto?')
        .ariaLabel('Excluir Imagem')
        .targetEvent(index)
        .cancel('Não')
        .ok('Sim');

      $mdDialog.show(confirm).then(
        function () {
          vm.imovel.imagens.splice(index, 1);
          vm.imagensTmp.splice(index, 1);
          vm.slides.splice(index, 1);
          MensagensService.exibirMensagemSucesso(
            'Imagem excluída com sucesso.'
          );
          $mdDialog.hide();
        },
        function () {
          $mdDialog.cancel();
        }
      );
    };

    vm.showUploadImagemDialog = function (ev) {
      $mdDialog.show({
        controller: uploadImagemDialogController,
        controllerAs: 'uploadImagemDialogCtrl',
        templateUrl:
          'scripts-cadastro-imovel/pages/imovel/view/album-fotos/dialog-upload-fotos.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: true,
        locals: {
          imagem: vm.imagem,
          imagensTmp: vm.imagensTmp,
          imagens: vm.imovel.imagens,
          slides: vm.slides,
          verificaObrigatorios: verificaObrigatorios
        }
      });
    };

    vm.ugProprietario = {};
    vm.ugProprietarioParcial = {};
    vm.ugProprietarioTitular = {};
    vm.orgaoEntidadeProprietario = {};
    vm.orgaoEntidadeTitular = {};
    vm.ugsTitular = undefined;
    vm.ugsAnterior = undefined;
    vm.orgaoEntidadeAnterior = {};
    vm.ugProprietarioAnterior = {};
    vm.orgaoEntidadeProprietarioParcial = {};

    vm.orgaoEntidadeProprietarioParcial.id = vm.orgaoEntidadeProprietario.id =
      vm.imovel.proprietario.codigoOrgaoEntidade
        ? vm.imovel.proprietario.codigoOrgaoEntidade
        : undefined;
    vm.orgaoEntidadeProprietarioParcial.nome =
      vm.orgaoEntidadeProprietario.nome = vm.imovel.proprietario.orgaoEntidade
        ? vm.imovel.proprietario.orgaoEntidade
        : undefined;
    vm.ugProprietarioParcial.id = vm.ugProprietario.id = vm.imovel.proprietario
      .codigoUg
      ? vm.imovel.proprietario.codigoUg
      : undefined;
    vm.ugProprietarioParcial.nome = vm.ugProprietario.nome = vm.imovel
      .proprietario.unidadeUsuario
      ? vm.imovel.proprietario.unidadeUsuario
      : undefined;
    if (vm.imovel.proprietarioTitular) {
      vm.orgaoEntidadeTitular.id = vm.imovel.proprietarioTitular
        .codigoOrgaoEntidade
        ? vm.imovel.proprietarioTitular.codigoOrgaoEntidade
        : undefined;
      vm.orgaoEntidadeTitular.nome = vm.imovel.proprietarioTitular.orgaoEntidade
        ? vm.imovel.proprietarioTitular.orgaoEntidade
        : undefined;
      vm.ugProprietarioTitular.id = vm.imovel.proprietarioTitular.codigoUg
        ? vm.imovel.proprietarioTitular.codigoUg
        : undefined;
      vm.ugProprietarioTitular.nome = vm.imovel.proprietarioTitular
        .unidadeUsuario
        ? vm.imovel.proprietarioTitular.unidadeUsuario
        : undefined;
    }
    if (vm.imovel.proprietarioAnterior) {
      vm.orgaoEntidadeAnterior.id = vm.imovel.proprietarioAnterior
        .codigoOrgaoEntidade
        ? vm.imovel.proprietarioAnterior.codigoOrgaoEntidade
        : undefined;
      vm.orgaoEntidadeAnterior.nome = vm.imovel.proprietarioAnterior
        .orgaoEntidade
        ? vm.imovel.proprietarioAnterior.orgaoEntidade
        : undefined;
      vm.ugProprietarioAnterior.id = vm.imovel.proprietarioAnterior.codigoUg
        ? vm.imovel.proprietarioAnterior.codigoUg
        : undefined;
      vm.ugProprietarioAnterior.nome = vm.imovel.proprietarioAnterior
        .unidadeUsuario
        ? vm.imovel.proprietarioAnterior.unidadeUsuario
        : undefined;
    }

    vm.verificaObrigatoriosProprietario = function () {
      var limpaOrgaoEntidadeProprietarioReal,
        limpaParcialmenteIntegralmenteProprietarioReal,
        limpaProprietarioParcial,
        limpaOrgaoEntidadeProprietarioTitular,
        limpaCpfCnpjDoOrgao,
        limpaProprietarioAnterior,
        limpaTipoProprietarioAnterior,
        limpaOrgaoEntidadeProprietarioAnterior,
        limpaEntidadeExtintaProprietarioAnterior,
        limpaTerceirosProprietarioAnterior,
        limpaJustificativaProprietarioAnterior,
        limpaCheckNaoPossuiCpfCnpj;

      eval.errorsForms = [];

      if (vm.imovel.proprietario.tipoProprietarioReal) {
        limpaOrgaoEntidadeProprietarioReal = !(
          vm.imovel.proprietario.tipoProprietarioReal.id ===
          ID_AUTARQUIAFUNDACAO_REAL
        );
        if (limpaOrgaoEntidadeProprietarioReal) {
          vm.imovel.proprietario.codigoUg = undefined;
          vm.imovel.proprietario.unidadeUsuario = undefined;
          vm.imovel.proprietario.codigoOrgaoEntidade = undefined;
          vm.imovel.proprietario.orgaoEntidade = undefined;
        }
      }

      limpaParcialmenteIntegralmenteProprietarioReal = !(
        !(
          vm.imovel.incorporacao.tipoIncorporacao.id === ID_IMOVEL_DE_TERCEIROS
        ) && vm.imovel.proprietario.tipoProprietarioReal.id
      );

      if (limpaParcialmenteIntegralmenteProprietarioReal) {
        vm.imovel.proprietario.isIntegralmente = undefined;
      }

      limpaProprietarioParcial =
        vm.imovel.proprietario.isIntegralmente == 'true';

      if (limpaProprietarioParcial) {
        vm.imovel.proprietario.tipoProprietarioParcial = undefined;
      }

      if (vm.imovel.proprietario.tipoProprietarioReal) {
        if (
          limpaProprietarioParcial &&
          vm.imovel.proprietario.tipoProprietarioReal.id !=
            ID_AUTARQUIAFUNDACAO_REAL
        ) {
          vm.imovel.proprietario.codigoOrgaoEntidade = undefined;
          vm.imovel.proprietario.orgaoEntidade = undefined;
        }
      }

      limpaOrgaoEntidadeProprietarioTitular =
        !vm.isTipoTitularAutarquiaFundacao();

      if (limpaOrgaoEntidadeProprietarioTitular) {
        vm.imovel.proprietarioTitular.codigoOrgaoEntidade = undefined;
        vm.imovel.proprietarioTitular.orgaoEntidade = undefined;
      }

      limpaCpfCnpjDoOrgao = !vm.isTipoTitularUniao();

      if (limpaCpfCnpjDoOrgao) {
        vm.imovel.proprietarioTitular.cnpjAdministracaoIndireta = undefined;
      }

      limpaProprietarioAnterior = !vm.isAquisicao();

      if (limpaProprietarioAnterior) {
        vm.imovel.proprietarioAnterior = undefined;
      }

      limpaTipoProprietarioAnterior = vm.proprietarioIgualAnterior();
      if (vm.imovel.proprietarioAnterior) {
        if (limpaTipoProprietarioAnterior) {
          vm.imovel.proprietarioAnterior.tipoProprietarioReal = undefined;
        }

        limpaOrgaoEntidadeProprietarioAnterior =
          !vm.isTipoProprietarioAnteriorAutarquiaFundacao();

        if (limpaOrgaoEntidadeProprietarioAnterior) {
          vm.imovel.proprietarioAnterior.codigoOrgaoEntidade = undefined;
          vm.imovel.proprietarioAnterior.orgaoEntidade = undefined;
        }

        limpaEntidadeExtintaProprietarioAnterior =
          !vm.proprietarioIgualAnterior();

        if (limpaEntidadeExtintaProprietarioAnterior) {
          vm.imovel.proprietarioAnterior.entidadeExtinta = undefined;
        }

        limpaTerceirosProprietarioAnterior = vm.terceiroNaoPossuiCpfOuCnpj();

        if (limpaTerceirosProprietarioAnterior) {
          vm.imovel.proprietarioAnterior.pessoas = [];
        }

        limpaJustificativaProprietarioAnterior =
          !limpaTerceirosProprietarioAnterior;

        if (limpaJustificativaProprietarioAnterior) {
          vm.imovel.proprietarioAnterior.justificativa = undefined;
        }

        limpaCheckNaoPossuiCpfCnpj =
          !vm.mostraOpcaoTerceiroNaoPossuiCpfOuCnpj();
        if (limpaCheckNaoPossuiCpfCnpj) {
          vm.imovel.proprietarioAnterior.naoPossuiCpfOuCnpj = undefined;
        }
      }

      angular.forEach(
        vm.forms.proprietarioForm.$error.required,
        function (value, key) {
          if (value.$name && value.$name != '') {
            eval.errorsForms.push(value.$name);
          }
        }
      );

      if (eval.errorsForms.length > 0) {
        MensagensService.exibirMensagemErro(
          'Necessário preencher os campos obrigatórios '
        );
        return;
      }

      vm.salvarImovel();
    };

    vm.idSenadoFederalAcessos = 2;
    vm.idTcuAcessos = 3;
    vm.idMinisterioDefesaAcessos = 17;
    vm.idSenadoFederalUgAcessos = 64;

    var uploadImagemDialogController = uploadImagemDialogController;

    uploadImagemDialogController.$inject = [
      '$scope',
      '$http',
      '$mdDialog',
      'imagem',
      'imagensTmp',
      'imagens',
      '$sce',
      'ImagemUpload',
      'slides',
      'verificaObrigatorios'
    ];

    function uploadImagemDialogController(
      $scope,
      $http,
      $mdDialog,
      imagem,
      imagensTmp,
      imagens,
      $sce,
      ImagemUpload,
      slides,
      verificaObrigatorios
    ) {
      $scope.imagem = imagem;
      $scope.imagensTmp = imagensTmp;
      $scope.imagens = imagens;
      $scope.slides = slides;
      $scope.imagem = {};
      $scope.errorsForms = [];
      $scope.submitted = false;
      this.hoje = new Date();
      $scope.fechar = function () {
        $mdDialog.cancel();
      };

      $scope.isCamposValido = function () {
        $scope.errorsForms = verificaObrigatorios.getValoresByForm(
          $scope.uploadImagem,
          'imovel'
        );
        if (
          $scope.uploadImagem.fotoImagem.$error &&
          $scope.uploadImagem.fotoImagem.$error.maxSize
        ) {
          $scope.errorsForms.push(
            'O arquivo da foto não pode ser superior a 5MB.'
          );
        }
        if ($scope.errorsForms.length == 0) {
          return true;
        }
        return false;
      };

      $scope.incluir = function () {
        $scope.submitted = true;
        if (
          validarExtensaoArquivo($scope.imagem.imagem) & $scope.isCamposValido()
        ) {
          $scope.imagensTmp.push($scope.imagem);
          salvarArquivo();
          $scope.imagem = {};
          vm.imagem = {};
          $mdDialog.hide();
        }
      };

      var salvarArquivo = function () {
        ImagemUpload.save(
          $scope.imagem,
          function (response) {
            $scope.imagens.push(response);
            renderizarImagemNaTela(response);
          },
          function (error) {
            MensagensService.exibirMensagemErro(error.data.message);
          }
        );
      };

      var renderizarImagemNaTela = function (response) {
        vm.slickConfig1Loaded = false;
        for (var i = 0; i < $scope.imagensTmp.length; i++) {
          if ($scope.imagensTmp[i].imagem.name == response.nomeImagem) {
            var reader = new FileReader();

            reader.onload = function (e) {
              var url = e.target.result;
              $scope.slides.push(url);
              $timeout(function () {
                vm.slickConfig1Loaded = true;
              }, 2000);
            };
            reader.readAsDataURL($scope.imagensTmp[i].imagem);
            break;
          }
        }
      };

      var validarExtensaoArquivo = function (arquivo) {
        if (arquivo == undefined) return false;
        else if (getExtensao(arquivo.name) == '.bmp') return true;
        else if (getExtensao(arquivo.name) == '.png') return true;
        else if (
          getExtensao(arquivo.name) == '.jpg' ||
          getExtensao(arquivo.name) == '.jpeg'
        )
          return true;
        else {
          MensagensService.exibirMensagemErro(
            'Formato de arquivo inválido. São permitidos apenas arquivos do tipo .BMP, JPG ou PNG.'
          );
          return false;
        }
      };

      var getExtensao = function (nomeArquivo) {
        if (nomeArquivo.endsWith('.jpeg')) {
          return nomeArquivo.substring(nomeArquivo.length - 5);
        }
        return nomeArquivo.substring(nomeArquivo.length - 4);
      };
    }

    vm.showCertificadoIncra = false;
    vm.showAreaTotalUniao = false;
    vm.isUniaoParcialmente = false;
    vm.isProprietarioUniao = false;
    vm.showAcenssaoBenfeitoria = true;
    vm.showAvancarBenfeitoria = false;
    vm.showDescricaoCartorial = false;
    vm.showNonAedificandi = true;
    vm.showInteresseSocial = true;
    vm.showPatrimonioTombado = true;
    vm.showSituacaoIncorporacao = true;
    vm.tipoUnidadeAutonomaIsRequired = false;
    vm.fracaoIdealIsRequired = false;
    vm.valorSituacaoIncorporacao = undefined;
    vm.codigoCartorioIsRequired = false;
    vm.matriculaIsRequired = false;
    vm.dataMatriculaIsRequired = false;
    vm.dataRegistroIsRequired = false;
    vm.direitoAdquiridoIsRequiredAndVisible = true;
    vm.isEspelhoOuCavidade = false;
    vm.valorDireitoAdquiridoIncorporado;
    vm.matriculaIsPreenchida = false;
    vm.dormitorioIsRequired = false;
    vm.valorIndiceAproveitamentoBenfeitoria = 0.0;
    vm.valorTaxaOcupacaoBenfeitoria = 0.0;
    vm.isAquisicaoSucessao = false;
    vm.isGaragem = false;
    var isAlterar = $stateParams.id ? true : false;

    TipoEquipamento.query(function (response) {
      vm.tipoEquipamento = response;
    });

    FormaLevantamento.query(function (response) {
      vm.formaLevantamento = response;
    });

    $scope.$watch('vm.imovel.naturezaImovel', function (value) {
      if (value !== undefined) {
        vm.showCertificadoIncra = value.id === 2 ? true : false;
      }
    });

    vm.defineShowAreaTotalUniao = function () {
      if (vm.isProprietarioUniao && vm.isUniaoParcialmente) {
        vm.showAreaTotalUniao = true;
      } else {
        vm.showAreaTotalUniao = false;
      }
    };

    $scope.$watch(
      'vm.imovel.proprietarioTitular.tipoProprietarioReal',
      function (value) {
        if (value !== undefined && value !== null) {
          vm.isProprietarioUniao = value.id === 1 ? true : false;
          vm.defineShowAreaTotalUniao();
        }
      }
    );

    $scope.$watch(
      'vm.imovel.proprietarioTitular.isIntegralmente',
      function (value) {
        if (value !== undefined) {
          vm.isUniaoParcialmente = value;
          vm.defineShowAreaTotalUniao();
        }
      }
    );

    $scope.$watch('vm.imovel.tipoImovel', function (newValue, oldValue) {
      if (vm.situacoesIncoporacao) {
        if (
          !TipoImovelFactory.isEspelhoDagua(newValue) &&
          !TipoImovelFactory.isCavidadeNaturalSubterranea(newValue) &&
          vm.situacoesIncoporacao.length == 3
        ) {
          vm.situacoesIncoporacao.splice(2, 1);
        }
      }

      if (angular.isDefined(oldValue) && angular.isDefined(newValue)) {
        vm.showAcenssaoBenfeitoria =
          newValue.id === 4 || newValue.id === 5 ? false : true;
        vm.showDescricaoCartorial = newValue.id === 4 ? false : true;
        vm.tipoUnidadeAutonomaIsRequired = newValue.id === 4;

        vm.valorTipoIncorporcao =
          newValue.id === 3 || newValue.id === 5 ? 1 : undefined;
        vm.valorSituacaoIncorporacao =
          newValue.id === 3 || newValue.id === 5 ? 3 : undefined;
        vm.valorSituacaoIncorporacao =
          TipoImovelFactory.isEspelhoDagua(newValue) ||
          TipoImovelFactory.isCavidadeNaturalSubterranea(newValue)
            ? 3
            : undefined;
        vm.isEspelhoOuCavidade = newValue.id === 3 || newValue.id === 5;
        vm.valorTipoProprietarioReal = newValue.id === 3 ? 1 : undefined;

        if (TipoImovelFactory.isEspelhoDagua(newValue)) {
          vm.imovel.proprietario.isIntegralmente = false;
        }
      }

      vm.tabs.atualizaTabs($scope.imovel.tipoImovel);
    });

    $scope.$watch('vm.imovel.terreno.benfeitoria', function (value) {
      if (value != undefined) {
        vm.showAvancarBenfeitoria = value == 'Sim' ? true : false;
      }

      vm.tabs.atualizaTabs($scope.imovel.tipoImovel);
    });

    $scope.$watch('imovel.incorporacao.tipoIncorporacao', function (value) {
      if (value != undefined) {
        vm.showSituacaoIncorporacao = value.id != 5 ? true : false;
        vm.direitoAdquiridoIsRequiredAndVisible =
          value.id == 1 || value.id == 5 ? false : true;
        vm.valorDireitoAdquiridoIncorporado = value.id == 1 ? 1 : undefined;
      }
    });

    $scope.$watch(
      'imovel.incorporacao.situacaoIncorporacao',
      function (newValue) {
        if (angular.isDefined(newValue)) {
          if (newValue) {
            vm.codigoCartorioIsRequired =
              newValue.id === 1 &&
              !TipoImovelFactory.isEspelhoDagua(vm.imovel.tipoImovel) &&
              !TipoImovelFactory.isCavidadeNaturalSubterranea(
                vm.imovel.tipoImovel
              );
            vm.matriculaIsRequired =
              newValue.id === 1 &&
              !TipoImovelFactory.isEspelhoDagua(vm.imovel.tipoImovel) &&
              !TipoImovelFactory.isCavidadeNaturalSubterranea(
                vm.imovel.tipoImovel
              );
            vm.dataMatriculaIsRequired = vm.matriculaIsRequired;
          }
        }
      }
    );

    $scope.$watch('imovel.incorporacao.numeroMatricula', function (value) {
      vm.matriculaIsPreenchida = value != undefined ? true : false;
    });

    $scope.$watch(
      'imovel.benfeitorias.especializacaoEdificacao',
      function (value) {
        if (value != undefined) {
          vm.dormitorioIsRequired =
            value.id == 1 || value.id == 3 ? true : false;
        }
      }
    );

    /* $scope.$watch('imovel.tipoUnidadeAutonoma', function(value) {
          if (value!=null && value != undefined) {
              vm.isGaragem = value.id == 3 ? true : false;
          }
      });*/

    $scope.$watch('imovel.tipoImovel', function (value) {
      if (value != null && value != undefined) {
        vm.isGaragem =
          value.tipoPai ==
            MAPA_CONSTANTS_INCORPORACAO.TIPO_IMOVEL.UNIDADE_AUTONOMA &&
          value.id == 8;
      }
    });

    $scope.$watch('imovel.incorporacao.tipoAquisicao', function (value) {
      if (value != undefined) {
        vm.isAquisicaoSucessao = value.id == 9 ? true : false;
      }
    });

    $scope.$on('pontoImovelValidado', function (data) {
      vm.imovel.pontoImovel = data.ponto;
      if (angular.isDefined(data.terrenoUnidadeAutonoma)) {
        vm.terreno = data.terrenoUnidadeAutonoma;
      }
    });

    $scope.$on('unidadeAutonomaAlterado', function (event, geoUnidadeAutonoma) {
      vm.imovel.unidadeAutonoma.geometriaUnidadeAutonoma = geoUnidadeAutonoma;
    });

    vm.mapaService.registrarObservadorCallback(
      vm.mapaService.GEOMETRIA_TERRENO,
      function (geometriaTerreno) {
        vm.imovel.terreno.geometriaTerreno = geometriaTerreno;
      }
    );

    $scope.file2blob = function (tipo, obj) {
      if (validarExtensaoArquivoDocumentacaoUA(obj)) {
        var r = new FileReader();
        r.onload = function () {
          switch (tipo) {
            case 'planoProtecaoIncendioFile':
              vm.imovel.unidadeAutonoma.planoProtecaoIncendio = r.result;
              break;
            case 'habiteseFile':
              vm.imovel.unidadeAutonoma.habitese = r.result;
              break;
            case 'alvaraConstrucaoFile':
              vm.imovel.unidadeAutonoma.alvaraConstrucao = r.result;
              break;
            case 'laudoPredialFile':
              vm.imovel.unidadeAutonoma.laudoPredial = r.result;
              break;
            case 'outroFile':
              vm.imovel.unidadeAutonoma.outro = r.result;
              break;
          }
        };
        r.readAsBinaryString(obj);
      } else {
        resetUploadsInvalidos(tipo);
      }
    };

    var resetUploadsInvalidos = function (tipo) {
      switch (tipo) {
        case 'planoProtecaoIncendioFile':
          $scope.imovel.unidadeAutonoma.planoProtecaoIncendio = undefined;
          break;
        case 'habiteseFile':
          $scope.imovel.unidadeAutonoma.habitese = undefined;
          break;
        case 'alvaraConstrucaoFile':
          $scope.imovel.unidadeAutonoma.alvaraConstrucao = undefined;
          break;
        case 'laudoPredialFile':
          $scope.imovel.unidadeAutonoma.laudoPredial = undefined;
          break;
        case 'outroFile':
          $scope.imovel.unidadeAutonoma.outro = undefined;
          break;
      }
    };

    var validarExtensaoArquivoDocumentacaoUA = function (arquivo) {
      if (getExtensaoDocumentacaoUA(arquivo.name) === '.pdf') return true;
      else if (getExtensaoDocumentacaoUA(arquivo.name) === '.png') return true;
      else if (
        getExtensaoDocumentacaoUA(arquivo.name) === '.jpg' ||
        getExtensaoDocumentacaoUA(arquivo.name) === '.jpeg'
      )
        return true;
      else {
        MensagensService.exibirMensagemErro(
          'Formato de arquivo inválido. São permitidos apenas arquivos do tipo PDF'
        );
        return false;
      }
    };

    var getExtensaoDocumentacaoUA = function (nomeArquivo) {
      if (nomeArquivo.endsWith('.jpeg')) {
        return nomeArquivo.substring(nomeArquivo.length - 5);
      }
      return nomeArquivo.substring(nomeArquivo.length - 4);
    };

    $scope.$on('removerUnidadeAutonoma', function (event, data) {
      if (data) {
        vm.unidadeAutonoma.geometriaUnidadeAutonoma = undefined;
      }
    });

    vm.plantaUnidade = {};
    vm.removerPlanta = function (index) {
      var confirm = $mdDialog
        .confirm({ focusOnOpen: false })
        .textContent('Deseja realmente excluir este arquivo?')
        .ariaLabel('Excluir Imagem')
        .targetEvent(index)
        .cancel('Não')
        .ok('Excluir');

      $mdDialog.show(confirm).then(
        function () {
          vm.imovel.unidadeAutonoma.plantasUnidade.splice(index, 1);
          MensagensService.exibirMensagemSucesso(
            'Planta excluída com sucesso.'
          );
        },
        function () {
          $mdDialog.cancel();
        }
      );
    };

    vm.resetarPlantaUnidade = function () {
      vm.plantaUnidade = {};
    };

    vm.showUploadPlantaUnidadeDialog = function (ev) {
      if (vm.imovel.unidadeAutonoma == undefined) {
        vm.imovel.unidadeAutonoma = {};
        vm.imovel.unidadeAutonoma.plantasUnidade = [];
      } else {
        if (vm.imovel.unidadeAutonoma.plantasUnidade == undefined) {
          vm.imovel.unidadeAutonoma.plantasUnidade = [];
        }
      }
      $mdDialog.show({
        controller: uploadPlantaUnidadeDialogController,
        templateUrl:
          'scripts-cadastro-imovel/pages/unidade-autonoma/view/dialog-upload-planta-unidade.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: true,
        locals: {
          plantaUnidade: vm.plantaUnidade,
          plantasUnidade: vm.imovel.unidadeAutonoma.plantasUnidade
        }
      });
    };

    var uploadPlantaUnidadeDialogController = function (
      $scope,
      $http,
      $mdDialog,
      PlantaUnidade,
      ExibirPlantaUnidade,
      plantaUnidade,
      plantasUnidade
    ) {
      $scope.plantaUnidade = plantaUnidade;
      $scope.plantasUnidade = plantasUnidade;

      $scope.fechar = function () {
        $mdDialog.cancel();
      };

      $scope.incluir = function () {
        if (validarPlantaUnica($scope.plantaUnidade)) {
          salvarArquivo($scope.plantaUnidade);
        }
      };

      var salvarArquivo = function (planta) {
        PlantaUnidade.save(
          planta,
          function (response) {
            $scope.plantasUnidade.push(response);
            $scope.plantaUnidade = {};
          },
          function (error) {
            MensagensService.exibirMensagemErro(error.data.message);
          }
        );
      };

      var validarPlantaUnica = function () {
        if ($scope.plantasUnidade.length <= 0) {
          return true;
        } else {
          for (var i = 0; i < $scope.plantasUnidade.length; i++) {
            if (
              $scope.plantasUnidade[i].planta.name ==
              $scope.plantaUnidade.planta.name
            ) {
              MensagensService.exibirMensagemErro(
                'O documento selecionado já foi enviado.'
              );
              return false;
            }
          }
          return true;
        }
      };

      $scope.removerPlanta = function (index) {
        if (confirm('Tem certeza que deseja excluir este arquivo?')) {
          $scope.plantasUnidade.splice(index, 1);
          MensagensService.exibirMensagemSucesso(
            'Planta excluída com sucesso.'
          );
        }
      };

      var validarExtensaoArquivo = function (arquivo) {
        if (getExtensao(arquivo.name) == '.pdf') return true;
        else if (getExtensao(arquivo.name) == '.png') return true;
        else if (getExtensao(arquivo.name) == '.jpg') return true;
        else {
          MensagensService.exibirMensagemErro(
            'Formato de arquivo inválido. São permitidos apenas arquivos do tipo .PDF, JPG ou PNG.'
          );
          return false;
        }
      };

      $scope.hoje = new Date();

      var getExtensao = function (nomeArquivo) {
        return nomeArquivo.substring(nomeArquivo.length - 4);
      };

      $scope.confirmar = function () {
        $mdDialog.hide();
      };

      $scope.visualizarPlanta = function (id) {
        ExibirPlantaUnidade.visualizarPlanta(id);
      };
    };
    uploadPlantaUnidadeDialogController.$inject = ["$scope", "$http", "$mdDialog", "PlantaUnidade", "ExibirPlantaUnidade", "plantaUnidade", "plantasUnidade"];

    vm.visualizarPlanta = function (id) {
      ExibirPlantaUnidade.visualizarPlanta(id);
    };

    vm.alterarParaArquivoUAShapeFile = function () {
      if (vm.unidadeAutonoma.tipoArquivo == 1212) {
        $('#desenhar-unidade-autonoma').css('display', 'none');
      } else {
        $('#desenhar-unidade-autonoma').css('display', 'block');
      }
    };

    vm.download = function (arquivo) {
      // if (vm.arquivo && vm.arquivo.arquivo) {
      //     var type = vm.arquivo.nome.endsWith(".zip") ? 'zip' : vm.arquivo.nome.endsWith(".csv") ? 'csv' : 'txt';
      //     var a = window.document.createElement("a");
      //     a.href = 'data:application/' + type + ';base64,' + vm.arquivo.arquivo;
      //     a.download = vm.arquivo.nome;
      //     document.body.appendChild(a);
      //     a.click();
      //     document.body.removeChild(a);
      //   }
    };

    vm.carregaArquivoShapeUA = function () {
      $mdDialog
        .show({
          controller: uploadArquivoUADialogController,
          controllerAs: 'uploadArqUACtrl',
          templateUrl:
            'scripts-cadastro-imovel/pages/unidade-autonoma/view/dialog-upload-arquivo-ua.html',
          parent: angular.element(document.body),
          clickOutsideToClose: true,
          locals: {
            arquivo: vm.arquivo,
            formaLevantamento: vm.formaLevantamento,
            tipoEquipamento: vm.tipoEquipamento
          }
        })
        .then(function () {});
    };

    var uploadArquivoUADialogController = function (
      $scope,
      $mdDialog,
      arquivo,
      formaLevantamento,
      tipoEquipamento
    ) {
      this.arquivo = arquivo;
      this.tipoEquipamento = tipoEquipamento;
      this.formaLevantamento = formaLevantamento;
      $scope.fechar = function () {
        $mdDialog.cancel();
      };
      $scope.confirmar = function () {
        var arquivo = {
          zipfile: $scope.arquivo.arquivo,
          escala: $scope.arquivo.escala,
          tipoFormaLevantamento: $scope.arquivo.tipoFormaLevantamento.id,
          dataPlanta: $scope.arquivo.dataPlanta
        };

        if ($scope.arquivo.resolucaoEspacial != undefined) {
          arquivo.resolucaoEspacial = $scope.arquivo.resolucaoEspacial;
        }

        if ($scope.arquivo.tipoEquipamento != undefined) {
          arquivo.tipoEquipamento = $scope.arquivo.tipoEquipamento;
        }

        JSON.stringify(arquivo);

        UnidadeAutonomaService.arquivo.save(
          arquivo,
          function (response) {
            if (isAlterar) {
              vm.notificarArquivoShapefileSalvoEdicao(response);
            } else {
              $scope.$emit('uploadShapefile', response.geometry);
              $scope.$emit(
                'uploadShapefileDescricaoCadastral',
                response.memorialDescritivoCadastral
              );
            }

            vm.arquivo = response;

            if (
              vm.arquivo.memorialDescritivoCadastral != null &&
              vm.arquivo.memorialDescritivoCadastral != undefined
            ) {
              vm.terreno.memorialDescritivoCadastral =
                vm.arquivo.memorialDescritivoCadastral;
              delete vm.arquivo.memorialDescritivoCadastral;
            }

            if (
              vm.arquivo.memorialDescritivoCadastral != null &&
              vm.arquivo.memorialDescritivoCadastral != undefined
            ) {
              $scope.imovel.terreno.areaTotalCadastral =
                vm.arquivo.areaGeometria;
              delete vm.arquivo.areaGeometria;
            }

            NotificadorService.notify(
              $scope,
              vm.arquivo,
              NOTIFICADORES.SHAPEFILECADASTRAL
            );
            $mdDialog.hide();
          },
          function (error) {
            MensagensService.exibirMensagemErro(error.data.message);
          }
        );
      };
    };
    uploadArquivoUADialogController.$inject = ["$scope", "$mdDialog", "arquivo", "formaLevantamento", "tipoEquipamento"];

    vm.notificarArquivoShapefileSalvoEdicao = function (response) {
      NotificadorService.notify(
        $scope,
        response.geometry,
        NOTIFICADORES.UPLOADSHAPEFILESUCCESSEDICAO
      );
      if (
        TipoTerreno.getTipoTerreno() === Dominio.Constantes.tipoTerrenoCartorial
      ) {
        NotificadorService.notify(
          $scope,
          response.memorialDescritivoCartorial,
          NOTIFICADORES.UPLOADSHAPEFILEDESCRICAOCARTORIALEDICAO
        );
        NotificadorService.notify(
          $scope,
          null,
          NOTIFICADORES.DESABILITARDESENHOCADASTRALEDICAO
        );
      } else {
        NotificadorService.notify(
          $scope,
          response.memorialDescritivoCartorial,
          NOTIFICADORES.UPLOADSHAPEFILEDESCRICAOCARTORIALEDICAO
        );
      }
    };

    vm.valoresFracaoIdeal = [];

    $scope.$watch(
      'imovel.unidadeAutonoma.fracaoIdeal',
      function (newValue, oldValue) {
        vm.valoresFracaoIdeal.push(oldValue);
      }
    );

    $scope.validarFracaoIdeal = function (ev, valor) {
      if ($scope.imovel.unidadeAutonoma.areaConstruida != null) {
        if (valor > $scope.imovel.terreno.areaTotalCadastral) {
          MensagensService.exibirMensagemErro(
            'Fração Ideal maior que Área Total Cadastral'
          );
          vm.imovel.unidadeAutonoma.fracaoIdeal = vm.valoresFracaoIdeal[0];
        }
      }
    };

    vm.fecharEdicao = function () {
      $state.go('INCORPORACAO.imovelConsultar');
    };

    $rootScope.$on('sincronizacaoExecutada', function () {
      vm.temPendenciaSincronizacao();
    });

    vm.isSincSpiunet = function () {
      return !!vm.imovelOriginal.dsSpiunet;
    };

    vm.showCancelarImovel = function (ev) {
      if (vm.imovel.rip) {
        VerificaUtilizacao.test(vm.imovel).then(function success(result) {
          if (result.status != 200 && result.status != 404) {
            //Se o imóvel não foi sincronizado, o serviço retorna 404 e deve seguir com o cancelamento
            MensagensService.exibirMensagemErro(
              'Não foi possível conectar com o serviço de destinação.'
            );
          } else {
            if (!result.data) {
              $mdDialog.show({
                controller: dialogCancelarImovelController,
                templateUrl:
                  'scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-cancelar-imovel.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose: true,
                locals: {
                  motivos: TipoCancelImovelPesquisa.query().$promise.then(
                    function (result) {
                      return result;
                    }
                  ),
                  Cancelamento: Cancelamento,
                  imovel: vm.imovel
                }
              });
            } else {
              MensagensService.exibirMensagemErro(
                'Este imóvel não pode ser cancelado pois há destinações ativas vinculadas a ele.'
              );
            }
          }
        });
      } else {
        var cancelamentoSerializado = {};
        cancelamentoSerializado.imovel = vm.imovel;
        var dialog = angular.copy($mdDialog);
        var confirm = dialog
          .confirm({
            focusOnOpen: false,
            skipHide: true,
            controller: function () {
              this.abort = function () {
                dialog.hide();
              };
              this.hide = function () {
                dialog.hide();
                $mdDialog.hide();
                Cancelamento.save(cancelamentoSerializado, function (data) {
                  MensagensService.exibirMensagemSucesso(
                    'Cancelamento realizado com sucesso'
                  );
                });
              };
            }
          })
          .content(
            'Após cancelar o imóvel, este não poderá mais ser ativado. Deseja continuar?'
          )
          .ariaLabel('Cancelar Imóvel')
          .cancel('Não')
          .ok('Sim');
        dialog.show(confirm).then(function (data) {
          if (vm.imovel.rip) location.reload();
          else
            $state.go(
              'scripts-cadastro-imovel/pages/imovel/view/consultar-imovel.tmpl.html'
            );
        });
      }
    };

    var dialogCancelarImovelController = function (
      $scope,
      $http,
      $mdDialog,
      motivos,
      imovel,
      Cancelamento
    ) {
      /*lista de motivos para cancelamento*/
      $scope.motivos = motivos;

      /*pega o id do imovel*/
      $scope.imovel = imovel;
      $scope.imovel.terreno = {};

      /*usuario logado no sistema*/
      $scope.imovelInicial = {
        usuarioCadastrador: 1,
        enderecamento: {
          pais: Dominio.Constantes.Brasil
        },
        incorporacao: {},
        terreno: {},
        benfeitorias: {}
      };

      $scope.fechar = function () {
        var certidaoCartorial;
        certidaoCartorial = {};
        $mdDialog.cancel();
      };

      /*atributos do formulario*/
      $scope.nomeCadastrador = 'Administrador 111.111.111-11';
      $scope.cancelamentoImovel = {};
      $scope.hoje = new Date();
      //$scope.cancelamentoImovel.dataCancelamento = new Date();
      $scope.cancelamentoImovel.usuarioCadastrador =
        $scope.imovelInicial.usuarioCadastrador;
      $scope.cancelamentoImovel.imovel = $scope.imovel;

      /*metodo que salva os dados do formulario*/
      $scope.salvarCancelamento = function (invalidForm) {
        $scope.submittedCancelamento = true;
        $scope.todosCamposPreenchidos = true;

        var cancelamentoSerializado = angular.toJson($scope.cancelamentoImovel);

        if (!invalidForm) {
          var dialog = angular.copy($mdDialog);
          var confirm = dialog
            .confirm({
              focusOnOpen: false,
              skipHide: true,
              controller: function () {
                this.abort = function () {
                  dialog.hide();
                };
                this.hide = function () {
                  dialog.hide();
                  $mdDialog.hide();
                  Cancelamento.save(cancelamentoSerializado, function (data) {
                    MensagensService.exibirMensagemSucesso(
                      'Cancelamento realizado com sucesso'
                    );
                  });
                };
              }
            })
            .content('O RIP será CANCELADO. Deseja continuar?')
            .ariaLabel('Cancelar Imóvel')
            .cancel('Não')
            .ok('Sim');
          dialog.show(confirm).then(function (data) {
            location.reload();
          });
        }
      };
    };
    dialogCancelarImovelController.$inject = ["$scope", "$http", "$mdDialog", "motivos", "imovel", "Cancelamento"];

    vm.isFormaIncorporacaoFracionamento = function () {
      return vm.imovel.incorporacao.tipoIncorporacao.id === 3;
    };

    vm.simulateQuery = false;
    vm.isDisabled = false;
    vm.querySearch = querySearch;

    function querySearch(query) {
      var results = query
          ? vm.extintas2.filter(createFilterFor(query))
          : vm.extintas2,
        deferred;
      if (vm.simulateQuery) {
        deferred = $q.defer();
        $timeout(
          function () {
            deferred.resolve(results);
          },
          Math.random() * 1000,
          false
        );
        return deferred.promise;
      } else {
        return results;
      }
    }

    function loadAll() {
      return vm.extintas.map(function (extinta) {
        return {
          value: extinta.descricao.toLowerCase(),
          display: extinta.descricao,
          id: extinta.id
        };
      });
    }

    function createFilterFor(query) {
      var lowercaseQuery = angular.lowercase(query);

      return function filterFn(extinta) {
        return extinta.value.indexOf(lowercaseQuery) >= 0;
      };
    }

    ExtintasService.query().$promise.then(function (result) {
      vm.extintas = result;
      vm.extintas2 = loadAll();
    });

    $scope.tabela = {
      limit: 5,
      limitsPage: [5, 10, 15],
      page: 1
    };

    var preparaCartorios = function () {
      for (var i = 0; i < vm.imovel.incorporacao.matriculas.length; i++) {
        preparaCartorio(vm.imovel.incorporacao.matriculas[i].cartorioID, i);
      }
    };

    var preparaCartorio = function (cartorioID, index) {
      CartorioService.pesquisaCartorioPorId
        .query({ id: cartorioID })
        .$promise.then(function (data) {
          for (var i = 0; i < vm.imovel.incorporacao.matriculas.length; i++) {
            if (i == index) {
              vm.imovel.incorporacao.matriculas[i].cartorio = data;
              return;
            }
          }
        });
    };

    vm.alteraMatricula = function (matricula) {
      vm.matricula.uf = matricula.uf;
      vm.matricula.cartorioID = matricula.cartorioID;
      vm.matricula.municipio = matricula.municipio;
      preparaMunicipio(vm.matricula.uf, vm.matricula.municipio);
      vm.matricula.cartorioEstrangeiro = matricula.cartorioEstrangeiro;
      vm.matricula.numero = matricula.numero;
      vm.matricula.dataMatricula = toDate(matricula.dataMatricula);
      vm.matricula.dataRegistro = toDate(matricula.dataRegistro);
      vm.matricula.index = vm.imovel.incorporacao.matriculas.indexOf(matricula);
    };

    var preparaMunicipio = function (uf, nome) {
      vm.pesquisaMunicipio(uf);
      vm.municipios.$promise.then(function (data) {
        for (var i = 0; i < data.length; i++) {
          if (data[i].nome === nome) {
            vm.matricula.municipio = data[i];

            preparaCartorioMatricula(
              vm.matricula.uf,
              vm.matricula.municipio.codigo,
              vm.matricula.cartorioID
            );
            return;
          }
        }
      });
    };

    vm.pesquisaMunicipio = function (uf) {
      vm.municipios = MunicipioService.TodosMunicipios.query({ uf: uf });
    };

    var preparaCartorioMatricula = function (uf, codigoMunicipio, cartorioID) {
      vm.carregarCartorios(uf, codigoMunicipio);
      vm.cartorios.$promise.then(function (data) {
        for (var i = 0; i < data.length; i++) {
          if (data[i].id == cartorioID) {
            vm.matricula.cartorio = data[i];
            return;
          }
        }
      });
    };

    vm.carregarCartorios = function (uf, numeroMunicipio) {
      if (uf === undefined || numeroMunicipio === undefined) {
        return;
      }
      vm.cartorios = CartorioService.pesquisaCartoriosPorUF.query({
        uf: uf,
        municipio: numeroMunicipio
      });
    };

    var toDate = function (dateStr) {
      if (typeof dateStr != 'string') {
        return dateStr;
      }

      var parts = dateStr.split('-');
      return new Date(parts[0], parts[1] - 1, parts[2]);
    };

    vm.confirmaRemocao = function (matricula) {
      var confirm = $mdDialog
        .confirm({ focusOnOpen: false })
        .textContent('Deseja realmente remover esta matrícula?')
        .ariaLabel('Remover')
        .cancel('Não')
        .ok('Remover');

      $mdDialog.show(confirm).then(
        function () {
          vm.removaMatricula(matricula);
          $mdDialog.hide();
        },
        function () {
          $mdDialog.cancel();
        }
      );
    };

    vm.removaMatricula = function (matricula) {
      var _index = vm.imovel.incorporacao.matriculas.indexOf(matricula);
      vm.imovel.incorporacao.matriculas.splice(_index, 1);
    };

    vm.adicionaMatricula = function (matricula) {
      if (!matricula) {
        return;
      }

      if (vm.imovel.enderecamento.pais.id === 1) {
        if (
          !matricula.uf ||
          !matricula.municipio ||
          !matricula.cartorio ||
          !matricula.numero ||
          !matricula.dataMatricula
        ) {
          MensagensService.exibirMensagemCamposObrigatorios();
          return;
        }
      } else {
        if (
          matricula.numero == undefined ||
          matricula.dataMatricula == undefined
        ) {
          MensagensService.exibirMensagemCamposObrigatorios();
          return;
        }
      }

      imovelService.validaDataMatricula(matricula);
      vm.matricula = matricula;
      vm.adicionaMatriculaEmIncorporacao(vm.matricula);
      vm.resetMatricula();
    };

    vm.resetMatricula = function () {
      vm.matricula.uf = '';
      vm.matricula.municipio = '';
      vm.matricula.cartorio = '';
      vm.matricula.cartorioEstrangeiro = '';
      vm.matricula.numero = '';
      vm.matricula.dataMatricula = null;
      vm.matricula.dataRegistro = null;
      vm.matricula.index = undefined;
    };

    vm.adicionaMatriculaEmIncorporacao = function (matricula) {
      var _matricula = angular.copy(matricula);
      if (_matricula.municipio != undefined) {
        _matricula.municipio = _matricula.municipio.nome;
      }
      if (_matricula.cartorio != undefined) {
        _matricula.cartorioID = _matricula.cartorio.id;
      }

      if (vm.matricula.index == null || vm.matricula.index == undefined) {
        vm.imovel.incorporacao.matriculas.push(_matricula);
      } else {
        vm.imovel.incorporacao.matriculas.splice(
          vm.matricula.index,
          1,
          _matricula
        );
      }
    };

    $scope.$on('buscaEdificacoesPorTerreno', function (event, args) {
      if (vm.imovel.terreno != undefined) {
        ImovelTerrenoService.getImoveisByUuidTerrenoService({
          uuidTerreno: vm.imovel.terreno.uuid
        }).$promise.then(function (imovel) {
          if (
            imovel.tipoImovel &&
            imovel.tipoImovel.tipoPai ==
              MAPA_CONSTANTS_INCORPORACAO.TIPO_IMOVEL.UNIDADE_AUTONOMA
          ) {
            vm.edificacoesUnidadeAutonoma = imovel.unidadesAutonomas;
            vm.showFormularioUnidadeAutonoma = function () {
              return false;
            };
            $rootScope.$broadcast('carregaEdificacoes', {
              any: vm.edificacoesUnidadeAutonoma
            });
          }
          vm.showMapaEdificacaoCadastrada = true;
        });
      }
    });

    $scope.$on('editarVoltarUnidadeAutonoma', function (event, args) {
      vm.showFormularioUnidadeAutonoma = function () {
        return true;
      };
      vm.showMapaEdificacaoCadastrada = false;
    });

    vm.showFormularioUnidadeAutonoma = function () {
      /*return (
              vm.tipoCadastroEdificacaoUnidadeAutonoma !== 'EXISTENTE' ||
              (vm.tipoCadastroEdificacaoUnidadeAutonoma === 'EXISTENTE' &&
                  vm.imovel.unidadeAutonoma.edificacao.id)
          );*/
      return (
        vm.imovel.tipoImovel.tipoPai ==
        MAPA_CONSTANTS_INCORPORACAO.TIPO_IMOVEL.UNIDADE_AUTONOMA
      );
    };

    vm.tipoVetorizacao = function () {
      if (
        vm.imovel.unidadeAutonoma != null &&
        vm.imovel.unidadeAutonoma.edificacao != null
      ) {
        return (
          vm.imovel.unidadeAutonoma.edificacao.tipoGeometria ===
          Dominio.Constantes.TiposDeDesenho.VETORIZACAO
        );
      }
      return false;
    };

    //        vm.cancelarSelecaoEdificacaoUnidadeAutonoma = function () {
    //            vm.tipoCadastroEdificacaoUnidadeAutonoma = "";
    //        };

    vm.isFusao = function () {
      return (
        $scope.imovel.incorporacao.tipoIncorporacao &&
        $scope.imovel.incorporacao.tipoIncorporacao.id === ID_POR_FUSAO
      );
    };

    vm.isAquisicao = function () {
      var isAquisicao;
      if ($scope.imovel.incorporacao.tipoIncorporacao) {
        isAquisicao =
          $scope.imovel.incorporacao.tipoIncorporacao.id === ID_POR_AQUISICAO;
      }
      return isAquisicao;
    };

    vm.isImoveisDeTerceiros = function () {
      return (
        $scope.imovel.incorporacao.tipoIncorporacao.id ===
        ID_IMOVEL_DE_TERCEIROS
      );
    };

    vm.isSucessaoPorEntidadesAPF = function () {
      var isSucessaoPorEntidadesAPF;
      if ($scope.imovel.incorporacao.tipoAquisicao) {
        isSucessaoPorEntidadesAPF =
          $scope.imovel.incorporacao.tipoAquisicao.codigoSistemico ===
          'INC_FORM_DADOS_GERAIS_SUCESSAO_ENT_APF';
      }
      return isSucessaoPorEntidadesAPF;
    };

    vm.isTerceiros = function () {
      var isTerceiros;
      if ($scope.imovel.incorporacao.tipoIncorporacao) {
        isTerceiros =
          $scope.imovel.incorporacao.tipoIncorporacao.id ===
          ID_IMOVEL_DE_TERCEIROS;
      }
      return isTerceiros;
    };

    function hasMatriculaIncorporacao() {
      return $scope.imovel.incorporacao.matriculas.length > 0;
    }

    function hasTituloAquisitivo() {
      return $scope.imovel.titulos.length > 0;
    }

    function hasCertidaoCartorial() {
      return (
        $scope.imovel.incorporacao.certidoesCartoriais &&
        $scope.imovel.incorporacao.certidoesCartoriais.length > 0
      );
    }

    function isSituacaoIncorporacaoIncorporado() {
      return (
        vm.imovel.incorporacao.situacaoIncorporacao &&
        vm.imovel.incorporacao.situacaoIncorporacao.id ===
          ID_SITUACAO_INCORPORACAO_INCORPORADO
      );
    }

    function atualizarAcorddion() {
      $scope.erros = angular.element('.ng-invalid');
      for (var erro = 0, len = $scope.erros.length; erro < len; erro++) {
        var scope = $scope.erros[erro];
        if (scope.tagName !== 'FORM' && scope.tagName !== 'NG-FORM') {
          var escopoElemento = angular.element(scope).scope();

          if (escopoElemento && escopoElemento.$pane) {
            escopoElemento.$pane.expand();
          } else if (scope.parentNode && scope.parentNode.parentElement) {
            if (scope.parentNode.parentElement.id === 'coordenadasForm') {
              abrirAcordionPonto();
            } else if (
              scope.parentNode.parentElement.id ===
                'secaoDadosGeraisDoImovel' ||
              (scope.parentNode.parentElement.parentElement &&
                scope.parentNode.parentElement.parentElement.id ===
                  'secaoDadosGeraisDoImovel')
            ) {
              abrirAcordionDetalhesDoImovel();
            } else if (seSecaoDadosIncorporacao(scope)) {
              abrirAcordionDadosIncorporacao();
            }
          }
        }
      }
    }

    function seSecaoDadosIncorporacao(scope) {
      if (
        scope.parentNode.id === 'secaoDadosIncorporacao' ||
        scope.parentNode.parentElement.id === 'secaoDadosIncorporacao' ||
        (scope.parentNode.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.id ===
            'secaoDadosIncorporacao') ||
        (scope.parentNode.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement.id ===
            'secaoDadosIncorporacao') ||
        (scope.parentNode.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.id === 'secaoDadosIncorporacao') ||
        (scope.parentNode.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement.id === 'secaoDadosIncorporacao') ||
        (scope.parentNode.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement.parentElement.id ===
            'secaoDadosIncorporacao') ||
        (scope.parentNode.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement.parentElement &&
          scope.parentNode.parentElement.parentElement.parentElement
            .parentElement.parentElement.parentElement.parentElement.id ===
            'secaoDadosIncorporacao')
      ) {
        return true;
      }

      return false;
    }

    function isCDRUGratuito() {
      return (
        $scope.imovel.incorporacao.tipoContratoPessoal &&
        $scope.imovel.incorporacao.tipoContratoPessoal.id == 8 &&
        $scope.imovel.incorporacao.isCdruOnerosa == false
      );
    }

    function isDireitoSuperficieGratuito() {
      return (
        $scope.imovel.incorporacao.tipoContratoPessoal &&
        $scope.imovel.incorporacao.tipoContratoPessoal.id == 7 &&
        $scope.imovel.incorporacao.isDireitoSuperficieOneroso == false
      );
    }

    function validarFormularioDadosGerais() {
      if ($scope.forms.imovelForm.$valid) {
        if (vm.isFusao() && vm.imovel.ripsFusao.length < 2) {
          MensagensService.exibirMensagemErro(
            'É necessário a inclusão de pelo menos 2 RIPs de imóveis remembrados'
          );
          abrirAcordionDadosIncorporacao();
          return false;
        }

        if (vm.isAquisicao() && !hasTituloAquisitivo()) {
          MensagensService.exibirMensagemErro(
            'É necessário a inclusão de, pelo menos, um título aquisitivo'
          );
          abrirAcordionDadosIncorporacao();
          return false;
        }

        //Título aquisitivo obrigatório para imóveis de terceiros e CDRU gratuita e Direito de Superfície Gratuito.
        if (vm.isTerceiros()) {
          if (isCDRUGratuito() || isDireitoSuperficieGratuito()) {
            if (!hasTituloAquisitivo()) {
              MensagensService.exibirMensagemErro(
                'É necessário a inclusão de pelo menos um título aquisitivo'
              );
              abrirAcordionDadosIncorporacao();
              return false;
            }
          }
        }

        if (
          vm.imovel.incorporacao.matriculas.length == 0 &&
          vm.matriculasImovel.length > 0
        ) {
          //console.log("Recuperando matrículas pois está se perdendo por algum motivo. Info da incorporação:");
          //console.log(vm.imovel.incorporacao);
          vm.imovel.incorporacao.matriculas = vm.matriculasImovel; //Recupera pois está perdendo as matrículas por algum motivo
          preparaCartorios();
        }
        if (
          vm.isAquisicao() &&
          !vm.isSucessaoPorEntidadesAPF() &&
          vm.imovel.proprietariosTerceiros.length > 0
        ) {
          //console.log("Limpando proprietariosTerceiros já que forma de incorporação é Aquisição");
          vm.imovel.proprietariosTerceiros = [];
          vm.imovel.tipoProprietarioReal = null;
          vm.imovel.proprietario.tipoProprietarioReal = {
            ativo: null,
            descricao: null,
            id: null,
            codigoSistemico: null
          };
        }
        if (
          !isOriginalmenteUniao() &&
          !isProcessoIncorporacao() &&
          !TipoImovelFactory.isEspelhoDagua(vm.imovel.tipoImovel)
        ) {
          if (!hasMatriculaIncorporacao()) {
            MensagensService.exibirMensagemErro(
              'É necessário informar uma matrícula'
            );
            abrirAcordionDadosIncorporacao();
            return false;
          }

          if (hasMatriculaIncorporacao() && !hasCertidaoCartorial()) {
            MensagensService.exibirMensagemErro(
              'É necessário incluir uma certidão cartorial'
            );
            abrirAcordionDadosIncorporacao();
            return false;
          }
        }
      } else {
        MensagensService.exibirMensagemCamposObrigatorios();
        atualizarAcorddion();
        abrirAcordionDadosIncorporacao();
        return false;
      }
      return true;
    }

    function isOriginalmenteUniao() {
      return (
        $scope.imovel.incorporacao.tipoIncorporacao.id ===
        ID_FORMA_INC_ORIGINALMENTE_DA_UNIAO
      );
    }

    function isProcessoIncorporacao() {
      return (
        $scope.imovel.incorporacao.situacaoIncorporacao.id ===
        ID_SITUACAO_INCORPORACAO_EMPROCESSO
      );
    }

    function abrirAcordionPonto() {
      document
        .getElementById('dadosLocalizacaoImovel')
        .classList.add('is-expanded');
      document.getElementById('dadosLocalizacaoImovelContent').style.maxHeight =
        'none';
      document
        .getElementById('coordenadasDoPonto')
        .classList.add('is-expanded');
      document.getElementById('coordenadasDoPontoContent').style.maxHeight =
        'none';
    }

    function abrirAcordionDetalhesDoImovel() {
      document
        .getElementById('dadosLocalizacaoImovel')
        .classList.add('is-expanded');
      document.getElementById('dadosLocalizacaoImovelContent').style.maxHeight =
        'none';
      document.getElementById('detalhesDoImovel').classList.add('is-expanded');
      document.getElementById('detalhesDoImovelContent').style.maxHeight =
        'none';
    }

    function abrirAcordionDadosIncorporacao() {
      document.getElementById('dadosIncorporacao').classList.add('is-expanded');
      document.getElementById('dadosIncorporacaoContent').style.maxHeight =
        'none';
    }

    function abrirAcordionDescricaoCadastral() {
      document
        .getElementById('descricaoCadastral')
        .classList.add('is-expanded');
      document.getElementById('descricaoCadastralContent').style.maxHeight =
        'none';
    }

    function abrirAcordionProprietario() {
      document.getElementById('secaoProprietario').classList.add('is-expanded');
      document.getElementById('secaoProprietarioContent').style.maxHeight =
        'none';
    }

    vm.submitFormularioDadosGerais = function (action) {
      if (validarFormularioDadosGerais()) {
        if (action !== 'salvar') {
          $scope.selectTab(1);
        } else {
          vm.salvarImovel();
        }
      } else {
        $scope.forms.imovelForm.$setSubmitted();
      }
    };

    vm.submitFormularioUnidadeAutonoma = function () {
      var respostaUnidadeAutonoma = validarFormularioUnidadeAutonoma();
      if (respostaUnidadeAutonoma.isValid) {
        $scope.selectTab(5);
        return true;
      } else {
        $scope.forms.adicionarUnidadeAutonomaForm.$setSubmitted();
        MensagensService.exibirMensagemErro(respostaUnidadeAutonoma.message);
        return false;
      }
    };

    vm.submitFormularioTerreno = function () {
      var isFormValid = $scope.forms.terrenoForm.$valid,
        possuiGeometria = !!vm.imovel.terreno.geometriaTerreno,
        //isGeometriaValida = vm.geometriaTerrenoCadastralValida,
        isGeometriaValida = true,
        isPontoNaInsercao = vm.pontoNaInsercao,
        isCavidadeNaturalSubterranea =
          TipoImovelFactory.isCavidadeNaturalSubterranea(vm.imovel.tipoImovel);

      var respostaValidacaoTerreno = TerrenoService.validarFormularioTerreno(
        isFormValid,
        possuiGeometria,
        isGeometriaValida,
        isPontoNaInsercao,
        isCavidadeNaturalSubterranea
      );

      if (vm.imovel.proprietario.tipoProprietarioReal.descricao === 'União') {
        vm.imovel.cnpjProprietario = '00.489.828/0009-02';
      }

      if (respostaValidacaoTerreno.isValid) {
        if (
          TipoImovelFactory.isGlebaTerrenoLoteComEdificacao(
            vm.imovel.tipoImovel
          ) ||
          TipoImovelFactory.isEspelhoDagua(vm.imovel.tipoImovel)
        ) {
          $scope.selectTab(2);
        } else if (TipoImovelFactory.isUnidadeAutonoma(vm.imovel.tipoImovel)) {
          $scope.selectTab(3);
        } else {
          $scope.selectTab(5);
        }
      } else {
        atualizarAcorddion();
        abrirAcordionDescricaoCadastral();
        $scope.forms.terrenoForm.$setSubmitted();
        MensagensService.exibirMensagemErro(respostaValidacaoTerreno.message);
      }
    };

    function validarFormularioUnidadeAutonoma() {
      var response = {
        isValid: true,
        message: ''
      };
      //console.log($scope.forms.adicionarUnidadeAutonomaForm)
      if ($scope.forms.adicionarUnidadeAutonomaForm.$valid) {
        if (self.imovel != undefined) {
          if (
            !TipoImovelFactory.isGaragem(self.imovel.tipoImovel) &&
            !self.imovel.unidadeAutonoma.edificacao.geometria
          ) {
            response.isValid = false;
            response.message = 'Favor informar a geometria da edificação';
          }
        }
      } else {
        response.isValid = false;
        response.message = 'Necessário preencher os campos obrigatórios';
      }
      return response;
    }

    vm.paginationTituloAquisitivo = {
      query: {
        limit: 5,
        page: 1
      },
      limitOptions: [5, 10, 15],
      listaResultante: []
    };

    vm.titulos = [];
    vm.titulo = {
      tipoTituloAquisitivo: undefined,
      tipoAtoLegal: undefined,
      publicacao: {},
      moeda: {},
      encargos: []
    };
    vm.tituloBackup = {};

    // INÍCIO CONTROLE DA ABA DE DOCUMENTOS

    vm.documento = {};
    vm.documentoBackup = {};
    vm.documentos = [];

    //FIM CONTROLE DA ABA DE DOCUMENTOS

    if (vm.imovel.titulos) {
      vm.imovel.titulos.forEach(function (titulo) {
        if (titulo.dataAssinatura) {
          titulo.dataAssinatura = new Date(titulo.dataAssinatura);
        }
        if (titulo.dataFinalVigencia) {
          titulo.dataFinalVigencia = new Date(titulo.dataFinalVigencia);
        }
        if (titulo.dataProrrogacaoVigencia) {
          titulo.dataProrrogacaoVigencia = new Date(
            titulo.dataProrrogacaoVigencia
          );
        }
        if (titulo.publicacao) {
          titulo.publicacao.dataPublicacao = new Date(
            titulo.publicacao.dataPublicacao
          );
        }
        if (titulo.encargos) {
          titulo.encargos.forEach(function (encargo) {
            encargo.prazoCumprimento = new Date(encargo.prazoCumprimento);
          });
        }
      });
    }

    if (vm.imovel.benfeitorias) {
      angular.forEach(vm.imovel.benfeitorias, function (benfeitoria) {
        if (isBenfeitoriaEdificacao(benfeitoria)) {
          angular.forEach(
            benfeitoria.edificacao.documentacao,
            function (documento) {
              if (
                documento &&
                !isEmpty(documento.possui) &&
                angular.isObject(documento.possui)
              ) {
                documento.possui = documento.possui.name;
              }
            }
          );
        }
      });
    }

    function isEmpty(value) {
      return (
        angular.equals({}, value) ||
        angular.isUndefined(value) ||
        value === null ||
        value === ''
      );
    }

    function isBenfeitoriaEdificacao(benfeitoria) {
      return (
        benfeitoria.tipoBenfeitoria && benfeitoria.tipoBenfeitoria.id === 1
      );
    }

    self.showModalImagemDialog = function (ev, index) {
      var imagens = [];
      angular.forEach($scope.imovel.imagens, function (value, key) {
        imagens.push($scope.imovel.imagens[key].imagem);
      });
      $mdDialog.show({
        controller: ["$scope", "slides", "startIndex", function ($scope, slides, startIndex) {
          $scope.slides = slides;
          $scope.startIndex = startIndex ? startIndex : 0;
          $scope.fechar = function () {
            $mdDialog.cancel();
          };
        }],
        controllerAs: 'showImagemModalCtrl',
        templateUrl:
          'scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos-detalhar-modal.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: true,
        locals: {
          slides: imagens,
          startIndex: index
        }
      });
    };

    var showImagemModalController = showImagemModalController;

    showImagemModalController.$inject = [
      '$scope',
      '$http',
      '$mdDialog',
      '$sce',
      'slides'
    ];

    function showImagemModalController($scope, $http, $mdDialog, $sce, slides) {
      $scope.slides = slides;

      $scope.fechar = function () {
        $mdDialog.cancel();
      };
    }

    vm.detalharHomologacao = function () {
      $state.go('INCORPORACAO.detalhesHomologacao', { id: vm.imovel.id });
    };

    vm.temPermissaoParaHomologarImovel = function () {
      var isPermissao = $rootScope.usuarioLogado.permissoes.find(function (el) {
        return el === PERMISSAO_HOMOLOGAR_IMOVEL;
      });

      return isPermissao ? true : false;
    };

    vm.enviarDocumentosSei = function () {
      $mdDialog.show({
        controller: enviarDocumentosSeiDialogController,
        templateUrl:
          'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-enviar-documentos-sei.html',
        parent: angular.element(document.body),
        clickOutsideToClose: false,
        locals: {
          imovel: vm.imovel
        }
      });
    };

    vm.alterarMostrarPendencia = function () {
      vm.mostrarPendencia = !vm.mostrarPendencia;
    };

    vm.temPendenciaMigracao = function () {
      return (
        vm.pendencias.filter(function (pendencia) {
          return pendencia.descricaoPendencia === 'VALIDAR_CADASTRO_SPIUNET';
        }).length > 0
      );
    };

    vm.temPendenciaSincronizacao = function () {
      return (
        vm.pendencias.filter(function (pendencia) {
          if (pendencia.descricaoPendencia === 'SINCRONIZAR_IMOVEL_SPIUNET') {
            vm.erroSincronizacao = pendencia.descricaoErro;
            return true;
          }
          return false;
        }).length > 0
      );
    };

    vm.getRestricoes = function () {
      var item = vm.imovel;
      if (item.isSuspenso) {
        var restricoes = null;
        restricoes = item.isSuspensoEdicaoCancelamento
          ? 'Edição/Cancelamento'
          : null;
        restricoes =
          restricoes == null
            ? item.isSuspensoFracionamento
              ? ' Fracionamento'
              : null
            : item.isSuspensoFracionamento
            ? restricoes + ', Fracionamento'
            : restricoes;
        restricoes =
          restricoes == null
            ? item.isSuspensoUnificacao
              ? ' Unificação'
              : null
            : item.isSuspensoUnificacao
            ? restricoes + ', Unificação'
            : restricoes;
        return '(PARA: ' + restricoes + ')';
      } else {
        return null;
      }
    };

    var enviarDocumentosSeiDialogController =
      enviarDocumentosSeiDialogController;

    enviarDocumentosSeiDialogController.$inject = [
      '$scope',
      '$http',
      '$mdDialog',
      'imovel',
      '$rootScope'
    ];

    function enviarDocumentosSeiDialogController(
      $scope,
      $http,
      $mdDialog,
      imovel
    ) {
      $scope.imovel = imovel;
      $scope.envioSei = {};
      $scope.envioSei.despachoConclusao = false;
      $scope.envioSei.folhaRosto = false;
      $scope.envioSei.memorialDescritivo = false;
      $scope.envioSei.shapefileTerrenoCartorial = false;
      $scope.envioSei.shapefileTerrenoCadastral = false;
      $scope.envioSei.shapefileBenfeitorias = false;
      $scope.envioSei.shapefileEdificacao = false;
      $scope.envioSei.tituloAquisitivo = false;
      $scope.envioSei.documentoCartorial = false;
      $scope.envioSei.arquivosComplementares = false;
      $scope.envioSei.documentacaoEdificacao = false;
      $scope.envioSei.arquivoPlanta = false;
      $scope.showEdificacaoAutonoma = false;
      $scope.showEdificacaoBenfeitoria = false;
      $scope.showPlantaBenfeitoria = false;
      $scope.showPlantaAutonoma = false;

      apresentaDocumentacaoEdificacao();
      apresentaDocumentacaoBenfeitoria();
      apresentaPlantasBenfeitoria();
      apresentaPlantaEdificacao();

      $scope.fechar = function () {
        $mdDialog.cancel();
        $timeout(function () {
          $state.go('INCORPORACAO.detalharImovel', { id: $scope.imovel.id });
        }, 500);
      };

      $scope.formatarNumero = function (numero) {
        return numero.replace(/(\d{5})(\d{6})(\d{4})(\d{2})/, '$1.$2/$3-$4');
      };

      function apresentaDocumentacaoBenfeitoria() {
        if (imovel.benfeitorias != null && imovel.benfeitorias != undefined) {
          angular.forEach(imovel.benfeitorias, function (benfeitoria) {
            if (
              benfeitoria.edificacao != null &&
              benfeitoria.edificacao != undefined
            ) {
              angular.forEach(
                benfeitoria.edificacao.documentacao,
                function (documento) {
                  if (
                    documento.arquivo != null &&
                    documento.arquivo != undefined
                  ) {
                    $scope.showEdificacaoBenfeitoria = true;
                  }
                }
              );
            }
          });
        }
      }

      function apresentaDocumentacaoEdificacao() {
        if (
          imovel.unidadeAutonoma != null &&
          imovel.unidadeAutonoma != undefined &&
          imovel.unidadeAutonoma.edificacao != null
        ) {
          angular.forEach(
            imovel.unidadeAutonoma.edificacao.documentacao,
            function (value) {
              if (value.arquivo != null && value.arquivo != undefined) {
                $scope.showEdificacaoAutonoma = true;
              }
            }
          );
        }
      }

      function apresentaPlantaEdificacao() {
        if (
          imovel.unidadeAutonoma != null &&
          imovel.unidadeAutonoma != undefined &&
          imovel.unidadeAutonoma.edificacao != null
        ) {
          angular.forEach(
            imovel.unidadeAutonoma.edificacao.plantas,
            function (value) {
              if (value.arquivo != null && value.arquivo != undefined) {
                $scope.showPlantaAutonoma = true;
              }
            }
          );
        }
      }

      function apresentaPlantasBenfeitoria() {
        if (imovel.benfeitorias != null && imovel.benfeitorias != undefined) {
          angular.forEach(imovel.benfeitorias, function (benfeitoria) {
            if (
              benfeitoria.plantas != null &&
              benfeitoria.plantas != undefined
            ) {
              angular.forEach(benfeitoria.plantas, function (planta) {
                if (planta.planta != null && planta.planta != undefined) {
                  $scope.showPlantaBenfeitoria = true;
                }
              });
            }
          });
        }
      }

      $scope.confirmar = function () {
        $scope.envioSei.imovel = $scope.imovel;
        homologarEnviarDestinacaoService.documentoSei($scope.envioSei).then(
          function (response) {
            $mdDialog.cancel();
            MensagensService.exibirMensagemSucesso(
              'Arquivos enviados para o SEI com sucesso'
            );
            $timeout(function () {
              $state.go('INCORPORACAO.detalharImovel', {
                id: $scope.imovel.id
              });
            }, 1000);
          },
          function (erro) {
            MensagensService.exibirMensagemErro(erro.data.message);
            if (!!$scope.imovel.migracaoSpiunet) {
              $mdDialog.cancel();
              $timeout(function () {
                $state.go('INCORPORACAO.detalharImovel', {
                  id: $scope.imovel.id
                });
              }, 1000);
            }
          }
        );
      };

      $scope.exibirNumeroProcessoFormatado = function () {
        return $scope.imovel.numeroProcesso.replace(
          /(\d{5})(\d{6})(\d{4})(\d{2})/,
          '$1.$2/$3-$4'
        );
      };

      $scope.showShapeFileBenfeitoria = function () {
        var apresenta = 0;

        angular.forEach($scope.imovel.benfeitorias, function (benfeitoria) {
          if (benfeitoria.arquivoGeometria != null) {
            apresenta++;
          }
        });

        return apresenta > 0;
      };

      $scope.maskCpf = function (input) {
        var str = input + '';
        if (str.length <= 11) {
          str = str.replace(/\D/g, '');
          str = str.replace(/(\d{3})(\d)/, '$1.$2');
          str = str.replace(/(\d{3})(\d)/, '$1.$2');
          str = str.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        }
        return str;
      };
    }

    vm.editandoUA = function () {
      return TipoImovelFactory.isUnidadeAutonoma(vm.imovel.tipoImovel);
    };

    vm.verificarRestricao = function (rip, event) {

        if (vm.temRestricao) {
          event.preventDefault();
          MensagensService.exibirMensagemErro(
            'RIP suspenso com restrição para essa operação'
          );
        }
    };

    vm.totalBenfeitoriasAtivas = function () {
      var i = 0;
      angular.forEach(vm.imovel.benfeitorias, function (benfeitoria) {
        if (benfeitoria.ativa === true) {
          i++;
        }
      });
      return i;
    };
  }
})();

(function(){

  'use strict';

  angular.module('su-cadastro-imovel').controller('DialogNaoPodeEditarController',
    ["$scope", "$http", "$mdDialog", "proprietario", "buscarCpfCnpjService", "MensagensService", "ProprietarioService", function ($scope, $http, $mdDialog, proprietario, buscarCpfCnpjService, MensagensService, ProprietarioService) {

      $scope.proprietario = proprietario;

      buscarCpfCnpjService.buscarUgs().then(function (response) {
        var lista = Array.isArray(response.data) ? response.data
                  : (response.data && Array.isArray(response.data.resposta) ? response.data.resposta : []);
        $scope.listaUgs = lista.map(function (ug) {
          return {
              codigo: ug.codigo,
              nome: ug.nome,
              display: ug.codigo + ' - ' + ug.nome
          };
        });
        $scope.ugSindica = $scope.listaUgs.find(function (ug) {
          return ug.codigo == $scope.proprietario.codigoUgSindica;
        });
        $scope.searchUG = $scope.proprietario.codigoUgSindica;
      });

      $scope.filtroUgs = function (texto) {
        return  $scope.listaUgs.filter(function(item) {
            var codigoEncontrado = String(item.codigo).includes(texto);
            var nomeEncontrado = item.nome.toLowerCase().includes(texto.toLowerCase());
            return codigoEncontrado || nomeEncontrado;
        });
      };

      $scope.setUgSindica = function (codigo) {
        $scope.proprietario.codigoUgSindica = codigo;
      };


      $scope.fechar = function () {
        $mdDialog.hide();
      };

      $scope.confirmar = function () {
        //salvar UG Síndica
        ProprietarioService.salvarUgSindica($scope.proprietario).then(

          function (response) {
            MensagensService.exibirMensagemSucesso('Proprietário salvo com sucesso.');
            $mdDialog.hide();
          },
          function (error) {
            MensagensService.exibirMensagemErro(error.data.message);
          }
        );
      };


    }]);
})();

(function () {
  'use strict';

  ConsultarImovelController.$inject = ["$scope", "$rootScope", "$mdDialog", "$location", "$window", "Dominio", "ectCepService", "UfsService", "PaisesUtilizadosService", "MunicipioService", "ImovelPesquisa", "SituacaoImovel", "Cancelamento", "$state", "DetalharImovel", "CidadesExtService", "VerificaUtilizacao", "MensagensService", "PaginacaoFactory", "$filter", "escopoCompartilhadoService", "camposAjuda", "GerarFolhaDeRosto", "CpfCnpjService", "AcaoExecutasService", "SincronizarService", "PendenciasService", "VerificarEdicao"];
  angular
    .module('su-cadastro-imovel')
    .controller('ConsultarImovelController', ConsultarImovelController);

  function ConsultarImovelController(
    $scope,
    $rootScope,
    $mdDialog,
    $location,
    $window,
    Dominio,
    ectCepService,
    UfsService,
    PaisesUtilizadosService,
    MunicipioService,
    ImovelPesquisa,
    SituacaoImovel,
    Cancelamento,
    $state,
    DetalharImovel,
    CidadesExtService,
    VerificaUtilizacao,
    MensagensService,
    PaginacaoFactory,
    $filter,
    escopoCompartilhadoService,
    camposAjuda,
    GerarFolhaDeRosto,
    CpfCnpjService,
    AcaoExecutasService,
    SincronizarService,
    PendenciasService,
    VerificarEdicao
  ) {
    var vm = this;

    vm.imovel = {};
    vm.imovelSelecionado = {};
    vm.situacoes = SituacaoImovel.query();
    vm.resultadoPesquisa = {};
    //vm.motivos = TipoCancelImovelPesquisa.query();
    vm.motivos = Dominio.Mocks.motivoCancelamento();
    vm.paises = PaisesUtilizadosService.query();
    vm.tiposTitular = Dominio.tiposProprietarioReal();
    vm.ufs = UfsService.UFS;
    vm.municipios = [];
    $scope.hoje = new Date();
    vm.usuarioLogado = $rootScope.usuarioLogado;
    vm.Cancelamento = Cancelamento;
    vm.cancelamentoImovel = {};
    vm.cidades = [];
    vm.paginacao = PaginacaoFactory.getDefault();
    vm.isCepGenerico = false;
    vm.matricula = null;
    vm.exibirImoveisCadastradosEditadosDoUsuario = false;
    vm.cpfCnpjProprietarios = [];
    vm.cpfUsuario = null;

    vm.pendenciaSelecionada = $location.search().pendencia;
    PendenciasService.getResumoPendencias()
      .query()
      .$promise.then(function (result) {
        vm.pendencias = result;
      });

    AcaoExecutasService.query().$promise.then(function (result) {
      vm.acoesExecutadas = result;
    });
    escopoCompartilhadoService.setObjetos('hints', camposAjuda);

    function removeZerosEsquerda(str) {
      return str.replace(/^0+(?=\d)/, '');
    }

    vm.tratamentoRip = function () {
      vm.imovel.rip =
        !vm.imovel.rip || !vm.imovel.rip.replaceAll('0', '')
          ? undefined
          : vm.imovel.rip.length < 9
          ? ('00000000' + vm.imovel.rip).slice(-8)
          : removeZerosEsquerda(vm.imovel.rip); // Se length < 9, é rip, caso contrario é ripSpiunet
      vm.imovel.ripSpiunet = vm.imovel.rip;
      return true;
    };

    vm.buscaEnderecoPorCep = function () {
      var cep = vm.imovel.enderecamento.cep;
      if (cep != undefined && cep.length == 8) {
        vm.progress();
        ectCepService.get({ cep: cep }).$promise.then(
          function (data) {
            data.pais = Dominio.Constantes.Brasil;
            vm.imovel.enderecamento = data;
            $scope.$emit('municipioImovelSelecionado', data.codigoIbge);

            if (vm.verificaCEPGenerico(cep)) {
              vm.isCepGenerico = true;
            } else {
              vm.isCepGenerico = false;
            }
            vm.closeProgress();
          },
          function (error) {
            vm.closeProgress();
            MensagensService.exibirMensagemErro('CEP não encontrado');
          }
        );
      }
    };

    vm.verificaCEPGenerico = function (cep) {
      return cep.endsWith('000');
    };

    $scope.limpar = function () {
      vm.imovel = {};
      vm.imovel.enderecamento = {};
      vm.matricula = null;
      vm.exibirImoveisCadastradosEditadosDoUsuario = false;
      vm.cpfCnpjProprietarios = [];
      vm.imovel.enderecamento.pais = Dominio.Constantes.Brasil;
      $scope.consultarImovel.$setPristine();
    };

    $scope.limparPesquisaPorRIP = function () {
      var rip = vm.imovel.rip;
      var ripSpiunet = vm.imovel.ripSpiunet;
      vm.imovel = {};
      vm.imovel.enderecamento = {};
      vm.matricula = null;
      vm.exibirImoveisCadastradosEditadosDoUsuario = false;
      vm.cpfUsuario = null;
      vm.cpfCnpjProprietarios = [];
      vm.imovel.enderecamento.pais = { id: null, descricao: null };
      vm.imovel.rip = rip;
      vm.imovel.ripSpiunet = ripSpiunet;
      if ($scope.consultarImovel) $scope.consultarImovel.$setPristine();
    };

    vm.cepPreenchido = false;
    $scope.$watch('vm.imovel.enderecamento.cep', function (value) {
      if (value == undefined || value == '') {
        vm.imovel.enderecamento = {};
        vm.imovel.enderecamento.pais = Dominio.Constantes.Brasil;
        vm.cepPreenchido = false;
      } else {
        vm.cepPreenchido = true;
      }
    });

    vm.cepValido = false;
    $scope.$watch('vm.imovel.enderecamento.municipio', function (value) {
      if (value == undefined || value == '') {
        vm.cepValido = false;
      } else {
        vm.cepValido = true;
      }
    });

    $scope.page = function () {
      var imovelSerializado = angular.toJson(vm.imovel);
      return ImovelPesquisa.query({
        imovelPesquisa: imovelSerializado,
        numeroPagina: vm.paginacao.page,
        maximoPorPagina: vm.paginacao.limit
      }).$promise.then(function (data) {
        if (data.content.length > 0) {
          vm.paginacao.totalElements = data.totalElements;
          vm.resultadoPesquisa = data.content;
        } else {
          vm.paginacao.totalElements = undefined;
          vm.resultadoPesquisa = [];
          MensagensService.exibirMensagemErro('Nenhum imóvel encontrado');
        }
      });
    };

    vm.apresentaBotao = function () {
      if (vm.cepPreenchido === true) {
        return vm.isCepGenerico && !vm.cepValido;
      } else {
        return true;
      }
    };

    vm.buscaCidadesPorPais = function () {
      if (vm.imovel.enderecamento.pais == null) return;

      vm.imovel.enderecamento.cidadeExterior = null;
      var pais = vm.imovel.enderecamento.pais.id;

      if (pais != 1 && pais != null) {
        CidadesExtService.query({ id_pais: pais }).$promise.then(function (
          result
        ) {
          vm.cidades = result;
        });
      }
    };

    vm.pesquisar = function () {
      if (vm.imovel.rip) {
        // se pesquisa por RIP, limpar todos os outros campos
        $scope.limparPesquisaPorRIP();
      }
      var idPais =
        vm.imovel.enderecamento.pais && vm.imovel.enderecamento.pais.id
          ? vm.imovel.enderecamento.pais.id
          : null;
      var siglaUf = null;
      if (vm.imovel.enderecamento.uf && vm.imovel.enderecamento.uf.sigla) {
        siglaUf = vm.imovel.enderecamento.uf.sigla;
      }

      if ($scope.formConsultarImovel.$valid) {
        var dataInicio = vm.imovel.periodoCadastramentoInicio
            ? $filter('date')(
                vm.imovel.periodoCadastramentoInicio,
                'MM/dd/yyyy'
              )
            : null,
          dataFim = vm.imovel.periodoCadastramentoFim
            ? $filter('date')(vm.imovel.periodoCadastramentoFim, 'MM/dd/yyyy')
            : null;
        ImovelPesquisa.query({
          rip: vm.imovel.rip,
          ripSpiunet: vm.imovel.ripSpiunet,
          idSituacaoImovel: vm.imovel.idSituacaoImovel,
          dataInicio: dataInicio,
          dataFim: dataFim,
          idProprietario: vm.imovel.idProprietario,
          idPais: idPais,
          cep: vm.imovel.enderecamento.cep,
          uf: siglaUf,
          municipio: !vm.imovel.enderecamento.municipio
            ? null
            : vm.imovel.enderecamento.municipio.nome,
          cidadeExterior: vm.imovel.enderecamento.cidadeExterior,
          numeroProcesso: vm.imovel.numeroProcesso,
          matricula: vm.matricula,
          cpfUsuario: vm.cpfUsuario,
          cpfCnpjProprietarios: vm.cpfCnpjProprietarios,
          pendencias: vm.imovel.pendencias,
          numeroPagina: vm.paginacao.page,
          maximoPorPagina: vm.paginacao.limit,
          acaoExecutada:
            vm.imovel.enderecamento.acaoExecutada == null
              ? null
              : vm.imovel.enderecamento.acaoExecutada
        }).$promise.then(function (data) {
          if (data.content.length > 0) {
            vm.paginacao.totalElements = data.totalElements;
            vm.resultadoPesquisa = data.content;
          } else {
            vm.paginacao.totalElements = undefined;
            vm.resultadoPesquisa = [];
            if (vm.imovel.rip || vm.imovel.idSituacaoImovel) {
              MensagensService.exibirMensagemErro('RIP não encontrado.');
            } else {
              MensagensService.exibirMensagemErro('Nenhum imóvel encontrado.');
            }
          }
        });
      } else {
        $scope.formConsultarImovel.$setSubmitted();
        MensagensService.exibirMensagemCamposObrigatorios();
      }
    };

    var localizacaoImovelController = function (
      $scope,
      $http,
      $mdDialog,
      imovel
    ) {
      $scope.imovel = imovel;

      $scope.fechar = function () {
        $mdDialog.cancel();
      };
    };
    localizacaoImovelController.$inject = ["$scope", "$http", "$mdDialog", "imovel"];

    vm.showLocalizacaoImovelDialog = function (ev, imovel) {
      $mdDialog.show({
        controller: localizacaoImovelController,
        templateUrl:
          'scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-localizacao-imovel.html',
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: true,
        locals: {
          imovel: imovel
        }
      });
    };

    /*pega o objeto selecionado na listagem*/
    vm.setImovelSelecionado = function (evento) {
      resetarImovelSelecionado();
      vm.imovelSelecionado = evento;
    };

    function resetarImovelSelecionado() {
      vm.imovelSelecionado = {};
    }

    var dialogCancelarImovelController = function (
      $scope,
      $http,
      $mdDialog,
      imovelSelecionado,
      TipoCancelImovelPesquisa,
      Cancelamento
    ) {
      $scope.imovelSelecionado = imovelSelecionado;

      /*usuario logado no sistema*/
      $scope.imovelInicial = {
        usuarioCadastrador: 1,
        enderecamento: {
          pais: Dominio.Constantes.Brasil
        },
        incorporacao: {},
        terreno: {},
        benfeitorias: {},
        tipoCancelamentoImovel: {}
      };

      /*lista de motivos para cancelamento*/
      //$scope.nomeCadastrador = 'Administrador 111.111.111-11';

      $scope.nomeCadastrador =
        vm.usuarioLogado.cpf + ' - ' + vm.usuarioLogado.nome;
      $scope.motivos = Dominio.Mocks.motivoCancelamento();

      /*atributos do formulario*/
      $scope.cancelamentoImovel = {};
      $scope.hoje = new Date();
      $scope.cancelamentoImovel.usuarioCadastrador =
        $scope.imovelInicial.usuarioCadastrador;
      $scope.cancelamentoImovel.imovel = $scope.imovelSelecionado;
      $scope.cancelamentoImovel.tipoCancelamentoImovel =
        vm.cancelamentoImovel.tipoCancelamentoImovel;

      /*metodo para fechar o dialog*/
      $scope.fechar = function () {
        vm.cancelamentoImovel = {};
        $mdDialog.hide();
      };

      /*metodo que salva os dados do formulario*/
      $scope.salvarCancelamento = function (invalidForm) {
        $scope.submittedCancelamento = true;
        //noinspection JSAnnotator
        eval.errorsForms = [];
        var cancelamentoSerializado = angular.toJson($scope.cancelamentoImovel);
        if (!invalidForm) {
          var dialog = angular.copy($mdDialog);
          var confirm = dialog
            .confirm({
              focusOnOpen: false,
              skipHide: true,
              controller: function () {
                this.abort = function () {
                  dialog.hide();
                };
                this.hide = function () {
                  dialog.hide();
                  $mdDialog.hide();
                  Cancelamento.save(cancelamentoSerializado, function () {
                    MensagensService.exibirMensagemSucesso(
                      'Cancelamento realizado com sucesso'
                    );
                    vm.pesquisar();
                  });
                };
              }
            })
            .content('O RIP será CANCELADO. Deseja continuar?')
            .ariaLabel('Cancelar Imóvel')
            .cancel('Não')
            .ok('Sim');
          dialog.show(confirm);
        } else {
          if ($scope.cancelamentoImovel.tipoCancelamentoImovel == undefined) {
            eval.errorsForms.push('Motivo');
          } else if (
            $scope.cancelamentoImovel.tipoCancelamentoImovel.id == 3 &&
            $scope.cancelamentoImovel.justificativa === undefined &&
            $scope.submittedCancelamento
          ) {
            eval.errorsForms.push('Justificativa');
          } else if (
            $scope.cancelamentoImovel.tipoCancelamentoImovel.id == 4 &&
            !$scope.cancelamentoImovel.numerProcesso
          ) {
            eval.errorsForms.push('Número de Processo');
          }
          MensagensService.exibirMensagemCamposObrigatorios();
        }
      };
    };
    dialogCancelarImovelController.$inject = ["$scope", "$http", "$mdDialog", "imovelSelecionado", "TipoCancelImovelPesquisa", "Cancelamento"];

    vm.verificaCancelamento = function (imovel, $event, $index) {
      VerificarEdicao.verificar(imovel.rip).then(function success(result) {
        if (!result.podeEditar){
          MensagensService.exibirMensagemErro(
          'Este imóvel possui utilizações ativas que devem ser canceladas previamente no módulo Gestão de Utilização. Após isso, retorne ao SPUnet para cancelar o RIP imóvel.'
        );
        } else {
          vm.showCancelarImovel($event,imovel,$index);
          vm.setImovelSelecionado(imovel);
        }
      });
    };

    /*aciona o dialog de cancelar imovel*/
    vm.showCancelarImovel = function (ev, imovel, index) {
      if (isSuspensoEdicaoCancelamento(imovel)) {
        MensagensService.exibirMensagemErro(
          'RIP suspenso com restrição para essa operação'
        );
      } else {
        if (imovel.rip) {
          VerificaUtilizacao.test(imovel).then(function success(result) {
            if (result.status != 200 && result.status != 404) {
              //Se o imóvel não foi sincronizado, o serviço retorna 404 e deve seguir com o cancelamento
              MensagensService.exibirMensagemErro(
                'Não foi possível conectar com o serviço de destinação.'
              );
            } else {
              if (
                result.data.resultado == null ||
                result.data.resultado.utilizacoesAtivas.length == 0
              ) {
                $mdDialog.show({
                  controller: dialogCancelarImovelController,
                  templateUrl:
                    'scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-cancelar-imovel.html',
                  parent: angular.element(document.body),
                  targetEvent: ev,
                  clickOutsideToClose: true,
                  locals: {
                    imovelSelecionado: imovel,
                    motivos: vm.motivos,
                    Cancelamento: vm.Cancelamento,
                    index: index,
                    resultadoPesquisa: vm.resultadoPesquisa
                  }
                });
              } else {
                var dialog = angular.copy($mdDialog);
                var confirm = dialog
                  .confirm({
                    focusOnOpen: false,
                    skipHide: true,
                    controller: function () {
                      this.abort = function () {
                        dialog.hide();
                      };
                      this.hide = function () {
                        dialog.hide();
                        $mdDialog.hide();
                      };
                    }
                  })
                  .content(
                    result.data.mensagens[0] +
                      result.data.mensagens[1] +
                      '\n' +
                      result.data.mensagens[2]
                  )
                  .ariaLabel('Cancelar Imóvel')
                  .ok('ok');

                dialog.show(confirm);
              }
            }
          });
        } else {
          var cancelamentoSerializado = {};
          cancelamentoSerializado.imovel = imovel;
          var dialog = angular.copy($mdDialog);
          var confirm = dialog
            .confirm({
              focusOnOpen: false,
              skipHide: true,
              controller: function () {
                this.abort = function () {
                  dialog.hide();
                };
                this.hide = function () {
                  dialog.hide();
                  $mdDialog.hide();
                  Cancelamento.save(cancelamentoSerializado, function (data) {
                    MensagensService.exibirMensagemSucesso(
                      'Cancelamento realizado com sucesso'
                    );
                    vm.pesquisar();
                  });
                };
              }
            })
            .content('O RIP será CANCELADO. Deseja continuar?')
            .ariaLabel('Cancelar Imóvel')
            .cancel('Não')
            .ok('Sim');
          dialog.show(confirm);
        }
      }
    };

    $scope.detalharImovel = function (id) {
      $state.go('INCORPORACAO.detalharImovel', { id: id });
    };

    $scope.buscarTodosImoveis = function () {
      var siglaUf = null;
      if (vm.imovel.enderecamento.uf && vm.imovel.enderecamento.uf.sigla) {
        siglaUf = vm.imovel.enderecamento.uf.sigla;
      }
      var idProprietarioTitular = vm.imovel.proprietarioTitular
        ? vm.imovel.proprietarioTitular.id
        : undefined;
      return ImovelPesquisa.query({
        rip: vm.imovel.rip,
        ripSpiunet: vm.imovel.ripSpiunet,
        situacaoImovel: vm.imovel.situacaoImovel,
        dataInicio: vm.imovel.periodoCadastramentoInicio,
        dataFim: vm.imovel.periodoCadastramentoFim,
        proprietarioTitular: idProprietarioTitular,
        pais: vm.imovel.idPais,
        cep: vm.imovel.enderecamento.cep,
        uf: siglaUf,
        municipio: !vm.imovel.enderecamento.municipio
          ? null
          : vm.imovel.enderecamento.municipio.nome,
        numeroProcesso: vm.imovel.numeroProcesso,
        matricula: vm.matricula,
        cpfUsuario: vm.cpfUsuario,
        cpfCnpjProprietarios: vm.cpfCnpjProprietarios,
        pendencias: vm.imovel.pendencias
      }).$promise.then(function (data) {
        return data.content;
      });
    };

    $scope.buscarImovelMapa = function (imovel) {
      return DetalharImovel.get({ id: imovel.id }).$promise.then(function (
        data
      ) {
        data.imovelOriginal.acaoExecutada =
          data.imovelOriginal.acaoExecutada.cdAcaoExecutada;
        return data.imovelOriginal;
      });
    };

    vm.irParaEdicao = function (item) {
      //vm.verificaEdicao(item);
      vm.continuarEdicao(item);
    };

    vm.continuarEdicao = function (item) {
      if (isSuspensoEdicaoCancelamento(item)) {
        MensagensService.exibirMensagemErro(
          'RIP suspenso com restrição para essa operação'
        );
      } else {
        $state.go('INCORPORACAO.alterarCadastro', { id: item.id, tab: -1 });
      }
    };

    vm.verificaEdicao = function (imovel) {
      VerificarEdicao.verificar(imovel.rip).then(function success(result) {
        if (!result.podeEditar){
          return $mdDialog.show({
            controller: 'DialogNaoPodeEditarController',
            templateUrl: 'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-nao-pode-editar-imovel.html',
            parent: angular.element(document.body),
            clickOutsideToClose: false,
            locals: {
              proprietario: imovel.proprietario,
            }
          });
        } else {
          vm.continuarEdicao(imovel);
        }
      });
    };

    function isCadastradorAutorizado(item) {
      return item.isCadastradorAutorizado;
    }

    function isRecusado(item) {
      return item.isRecusado;
    }

    function isSuspensoEdicaoCancelamento(item) {
      return item.isSuspensoEdicaoCancelamento;
    }

    vm.getRestricoes = function (item) {
      if (item.isSuspenso) {
        var restricoes = null;
        restricoes = item.isSuspensoEdicaoCancelamento
          ? 'Edição/Cancelamento'
          : null;
        restricoes =
          restricoes == null
            ? item.isSuspensoFracionamento
              ? ' Fracionamento'
              : null
            : item.isSuspensoFracionamento
            ? restricoes + ', Fracionamento'
            : restricoes;
        restricoes =
          restricoes == null
            ? item.isSuspensoUnificacao
              ? ' Unificação'
              : null
            : item.isSuspensoUnificacao
            ? restricoes + ', Unificação'
            : restricoes;
        return '(PARA: ' + restricoes + ')';
      } else {
        return null;
      }
    };

    function isHomologado(item) {
      return item.situacaoImovel.id === 1;
    }

    function isCancelado(item) {
      return item.situacaoImovel.id === 3;
    }

    vm.irParaDetalhamento = function (idImovel) {
      $state.go('INCORPORACAO.detalharImovel', { id: idImovel });
    };

    function isSincSpiunet(item) {
      return !!item.dsSpiunet;
    }

    vm.exibeCancelar = function (item) {
      return !isCancelado(item);
      //&& !isSuspensoEdicaoCancelamento(item)
      //&& !isSincSpiunet(item)
    };

    vm.exibeEdicao = function (item) {
      return (
        !isCancelado(item) &&
        //- !isSuspensoEdicaoCancelamento(item) &&
        isCadastradorAutorizado(item)
        //&& (isHomologado(item) || isRecusado(item)) US0020
      );
    };

    vm.gerarFolhaDeRosto = function (imovelId) {
      GerarFolhaDeRosto.getFile({ id: imovelId }).$promise.then(function (
        response
      ) {
        var link = document.createElement('a');
        link.download = 'FOLHA_DE_ROSTO';
        link.target = '_blank';
        link.href = URL.createObjectURL(response.response);
        document.body.appendChild(link);
        link.click();
      });
    };

    $scope.$watch(
      'vm.exibirImoveisCadastradosEditadosDoUsuario',
      function (value) {
        vm.cpfUsuario = null;
        if (value && $scope.usuarioLogado && $scope.usuarioLogado.cpf) {
          vm.cpfUsuario = $scope.usuarioLogado.cpf;
        }
      }
    );

    vm.formataCpfCnpj = function (documento) {
      return CpfCnpjService.formataCpfCnpj(documento);
    };

    vm.progress = function () {
      $mdDialog.show(
        $mdDialog.alert({
          templateUrl:
            'scripts-cadastro-imovel/pages/imovel/view/modals/progress.tmpl.html'
        })
      );
    };

    vm.closeProgress = function () {
      $mdDialog.hide();
    };

    vm.pesquisaMunicipio = function (uf) {
      if (uf) {
        vm.progress();
        MunicipioService.TodosMunicipios.query({ uf: uf }).$promise.then(
          function (result) {
            vm.municipios = result;
            vm.closeProgress();
          }
        );
      } else {
        vm.municipios = [];
      }
    };

    vm.getTituloPendencia = function (item) {
      var pendenciaMigracao;
      var pendenciaSincronizacao;

      item.pendencias.forEach(function (pendencia) {
        if (pendencia.descricaoPendencia === 'VALIDAR_CADASTRO_SPIUNET') {
          pendenciaMigracao = pendencia;
        }
        if (pendencia.descricaoPendencia === 'SINCRONIZAR_IMOVEL_SPIUNET') {
          pendenciaSincronizacao = pendencia;
        }
      });

      if (!!pendenciaMigracao) {
        return 'Imóvel oriundo da migração de imóveis do sistema SPIUnet, é necessária a validação dos dados desse imóvel.';
      }
      /*if (!!pendenciaSincronizacao) {
        var erro = !pendenciaSincronizacao.descricaoErro
          ? ''
          : ' Erro ao sincronizar: ' + pendenciaSincronizacao.descricaoErro;
        return 'Pendente sincronização com o SPIUnet.' + erro;
      }*/
      return null;
    };

    vm.exibeSincronizacao = function (item) {
      return item.dsSpiunet === 'pend. sincronizacao';
    };

    vm.exportarPesquisa = function () {
      var idPais = vm.imovel.enderecamento.pais.id;
      var siglaUf = null;
      if (vm.imovel.enderecamento.uf && vm.imovel.enderecamento.uf.sigla) {
        siglaUf = vm.imovel.enderecamento.uf.sigla;
      }

      var dataInicio = vm.imovel.periodoCadastramentoInicio
          ? $filter('date')(vm.imovel.periodoCadastramentoInicio, 'MM/dd/yyyy')
          : null,
        dataFim = vm.imovel.periodoCadastramentoFim
          ? $filter('date')(vm.imovel.periodoCadastramentoFim, 'MM/dd/yyyy')
          : null;

      ImovelPesquisa.downloadFile({
        rip: vm.imovel.rip,
        ripSpiunet: vm.imovel.ripSpiunet,
        idSituacaoImovel: vm.imovel.idSituacaoImovel,
        dataInicio: dataInicio,
        dataFim: dataFim,
        idProprietario: vm.imovel.idProprietario,
        idPais: idPais,
        cep: vm.imovel.enderecamento.cep,
        uf: siglaUf,
        municipio: !vm.imovel.enderecamento.municipio
          ? null
          : vm.imovel.enderecamento.municipio.nome,
        numeroProcesso: vm.imovel.numeroProcesso,
        matricula: vm.matricula,
        cpfUsuario: vm.cpfUsuario,
        cpfCnpjProprietarios: vm.cpfCnpjProprietarios,
        pendencias: vm.imovel.pendencias,
        acaoExecutada:
          vm.imovel.enderecamento.acaoExecutada == null
            ? null
            : vm.imovel.enderecamento.acaoExecutada
      }).$promise.then(function (result) {
        var link = document.createElement('a');
        link.download = 'spunet_consulta_imoveis.xls';
        link.target = '_blank';
        link.href = URL.createObjectURL(result.response);
        link.style = 'display: none';
        document.body.appendChild(link);
        link.click();
      });
    };
  }
})();

(function () {
    'use strict';
    ConsultarCpfController.$inject = ["$scope", "buscarCpfCnpjService", "MensagensService", "PaginacaoFactory", "$mdDialog", "cpfCnpjMaskService"];
    angular.module('su-cadastro-imovel').controller('ConsultarCpfController', ConsultarCpfController);
    function ConsultarCpfController(
        $scope, 
        buscarCpfCnpjService, 
        MensagensService, 
        PaginacaoFactory, 
        $mdDialog,
        cpfCnpjMaskService) {

        var vm = $scope;

        vm.parciais = {};
        vm.terceiros = {};
        vm.cartorio = {};
        vm.autarquia = {}

        vm.parciais.proprietarios = {};
        vm.terceiros.proprietarios = {};
        vm.cartorio.proprietarios = {};
        vm.inputUgCnpj = '';

        vm.tiposProprietariosParciais = [
            'União',
            'Autarquia',
            'Fundação',
            'Terceiros'
        ];

        vm.paginacaoCpfCnpj = PaginacaoFactory.getDefault();

        vm.mascaraCpfCnpj = function (cpf, proprietario) {                        
            if (cpf != null) {                
                cpf = cpfCnpjMaskService.cleanMask(cpf);
                if (cpf.length > 14) {
                    cpf = '';
                    vm.whichCpf(cpf, proprietario);
                    return
                }
                cpf = cpfCnpjMaskService.getMask(cpf);
                vm.whichCpf(cpf, proprietario);
            }
        };

        //var lastDigitUg;
        vm.mascaraUgCnpj = function (ug) {
            
            if (ug != null) {

                ug = cpfCnpjMaskService.cleanMask(ug);
                
                if (ug.length > 14) {
                    ug = '';
                    vm.inputUgCnpj = ug;
                    return
                }
                ug = cpfCnpjMaskService.getMaskCnpj(ug);                
                vm.inputUgCnpj = ug;
                
            }
        };

        vm.validarCpfCnpj = function (cpfCnpj, proprietario) {
            if (cpfCnpj != null) {
                cpfCnpj = cpfCnpj.replace(/[^A-Z0-9]/g, '');
                cpfCnpj = cpfCnpj.replace(/^\s+|\s+$/g, '');                          
                if(cpfCnpj.length === 11){
                    buscarCpfCnpjService.buscarPessoa(cpfCnpj).then(function (response) {
                        var nome = response.data.nome;

                        if (nome == undefined){
                            cpfCnpj = '';
                            vm.whichNome(cpfCnpj, proprietario);
                            vm.whichCpf(cpfCnpj, proprietario);
                            MensagensService.exibirMensagemErro("CPF não encontrado");
                        } else {
                          vm.whichNome(nome, proprietario);
                        }
                    }, function errorCallback() {
                        cpfCnpj = '';
                        vm.whichNome(cpfCnpj, proprietario);
                        vm.whichCpf(cpfCnpj, proprietario);
                        vm.limparNomeProprietario(proprietario);
                        MensagensService.exibirMensagemErro("CPF não encontrado");
                    }
                    )
                }
                else if (cpfCnpj.length === 14){
                    buscarCpfCnpjService.buscarCnpj(cpfCnpj).then(function (response) {
                        var nome = response.data.nomeEmpresarial;
                        if (nome == undefined){
                            cpfCnpj = '';
                            vm.whichNome(cpfCnpj, proprietario);
                            vm.whichCpf(cpfCnpj, proprietario);
                            MensagensService.exibirMensagemErro("CNPJ não encontrado");
                          } else {
                            vm.whichNome(nome, proprietario);
                          }
                        }, function errorCallback() {
                            cpfCnpj = '';
                            vm.whichNome(cpfCnpj, proprietario);
                            vm.whichCpf(cpfCnpj, proprietario);
                            vm.limparNomeProprietario(proprietario);
                            MensagensService.exibirMensagemErro("CNPJ não encontrado");
                        }
                    )
                }
            }else{
                cpfCnpj = '';
                vm.whichNome(cpfCnpj, proprietario);
                vm.whichCpf(cpfCnpj, proprietario);
                MensagensService.exibirMensagemErro("CPF ou CNPJ não podem ser nulos!");
            }
        };


        vm.adicionaCpfCnpj = function (pessoa, proprietario) {
            var _proprietario = [];
            var empty = '';
            if (proprietario === 'terceiros') {
              _proprietario = vm.imovel.proprietariosTerceiros
            } else if (proprietario === 'parcial') {
                _proprietario = vm.imovel.proprietariosParciais
            } else if (proprietario === 'cartorio'){
                _proprietario = vm.imovel.proprietariosCartorio
            }

            for (var i = 0; i < _proprietario.length; i++) {
                if (pessoa.cpf == _proprietario[i].cpf) {
                    MensagensService.exibirMensagemErro("CPF repetido");
                    vm.whichNome(empty, proprietario);
                    vm.whichCpf(empty, proprietario);
                    return
                }
            }
            var _pessoa = angular.copy(pessoa);
            //_proprietario.push(_pessoa);
            _proprietario.unshift(_pessoa);

            vm.whichNome(empty, proprietario);
            vm.whichCpf(empty, proprietario);
        };


        vm.whichCpf = function (cpf, proprietario) {
            switch (proprietario) {
                case 'terceiros':
                    vm.terceiros.proprietarios.cpf = cpf;
                    vm.terceiros.isCpfValido = false;
                    break;
                case 'locatario':
                    vm.imovel.proprietario.codigoOrgaoEntidade = cpf;
                    vm.terceiros.isCpfValido = false;
                    break;
                case 'parcial':
                    if (cpf == '') {
                        vm.parciais.proprietarios.tipo = cpf;
                    }
                    vm.parciais.proprietarios.cpf = cpf;
                    vm.parciais.isCpfValido = false;
                    break;
                case 'cartorio':
                    vm.cartorio.proprietarios.cpf = cpf;
                    vm.cartorio.isCpfValido = false;
                    break;
            }
        };

        vm.whichNome = function (nome, proprietario) {
            switch (proprietario) {
                case 'terceiros':
                    vm.terceiros.proprietarios.nome = nome;
                    if (nome != '') {
                        vm.terceiros.isCpfValido = true;
                    }
                    break;
                case 'locatario':
                    vm.imovel.proprietario.orgaoEntidade = nome;
                    if (nome != '') {
                        vm.terceiros.isCpfValido = true;
                    }
                    break;
                case 'parcial':
                    vm.parciais.proprietarios.nome = nome;
                    if (nome != '') {
                        vm.parciais.isCpfValido = true;
                    }
                    break;
                case 'cartorio':
                    vm.cartorio.proprietarios.nome = nome;
                    if (nome != '') {
                        vm.cartorio.isCpfValido = true;
                    }
                    break;
            }
        };

        vm.excluirPessoa = function (indice, proprietario) {

          var confirm = $mdDialog.confirm({focusOnOpen: false})
            .textContent("Deseja remover este item?")
            .ariaLabel("Excluir item")
            .cancel("Não")
            .ok("Excluir");

          $mdDialog.show(confirm).then(function(){
              if (proprietario === 'terceiros') {
                  proprietario = vm.imovel.proprietariosTerceiros
              } else if (proprietario === 'parcial') {
                  proprietario = vm.imovel.proprietariosParciais
              } else if (proprietario === 'cartorio'){
                  proprietario = vm.imovel.proprietariosCartorio
              }

              for (var i = 0; i < proprietario.length; i++) {
                  if (proprietario[i].cpf === indice) {
                      proprietario.splice(i, 1);
                  }
              }
              MensagensService.exibirMensagemSucesso("Item excluído com sucesso.");
              $mdDialog.hide();

          }, function(){
              $mdDialog.cancel();
          });
        };

        vm.limparTabela = function (proprietario) {
            switch (proprietario) {
                case 'terceiros':
                    vm.imovel.proprietariosTerceiros = [];
                    break;
                case 'parcial':
                    vm.imovel.proprietariosParciais = [];
                    break;
                case 'cartorio':
                    vm.imovel.proprietariosCartorio = [];
                    break;
            }
        };

        vm.resetOriundo = function () {
            vm.imovel.registradoCartorio = undefined;
        };

        vm.showTipoUniao = function (tipo) {
            return !(tipo == 'União' && vm.imovel.proprietario.tipoProprietarioReal.descricao == 'União');
        };

        vm.limparNomeProprietario = function (proprietario) {
            switch (proprietario) {
                case 'terceiros':
                    vm.terceiros.proprietarios.nome = undefined;
                    break;
                case 'locatario':
                    vm.terceiros.proprietarios.nomeLocatario = undefined;
                    break;
                case 'parcial':
                    vm.parciais.proprietarios.nome = undefined;
                    break;
                case 'cartorio':
                    vm.cartorio.proprietarios.nome = undefined;
                    break;
            }
        }
    }
})();

(function () {
    'use strict';
    angular.module('su-cadastro-imovel')
        .factory("HomologacaoImovelService", ["$http", "urlBase", function($http, urlBase){
            function buscarRecusado(id) {
                return $http.get(urlBase + 'imovel/homologacao/recusado/' + id);
            }

            function buscarNaoHomologado(id) {
                return $http.get(urlBase + 'imovel/homologacao/' + id);
            }

            function obterArquivoDocumento2(documento){
              return $http.get(urlBase + 'arquivo/documento-arquivo/listar/' + documento.arquivo.id, {
                responseType: 'arraybuffer'
              });
            }

            function buscarTodos(filtro, pagina, limite) {
                var parametros = {
                    imovelPesquisa : filtro,
                    page : pagina,
                    max : limite
                };

                return $http.get(urlBase + 'imovel/homologacao/', {params: parametros});
            }

            return {
                buscarRecusado: buscarRecusado,
                buscarNaoHomologado: buscarNaoHomologado,
                buscarTodos: buscarTodos
            };
        }])
        .factory("SituacaoImovel", ["$resource", "urlBase", function($resource, urlBase){
            return $resource(urlBase + "dominio/situacao-imovel/all");
        }])

        .service('ContadorService', ["$resource", function($resource){
            var contador = 0;

            this.getContador = function() {
                return contador;
            }

            this.setContador = function(cont){
                contador = cont;
            }
        }])

        .factory("TerrenoImovelService", ["$resource", "urlBase", function($resource, urlBase){
            return $resource(urlBase + "imovel/detalhar/terreno/:id", {id : '@id'}, {
                get : {
                    method : 'GET',
                    transformRequest: function (data) {
                        return angular.toJson(data);
                    }
                }
            });
        }]);

})();

(function () {
  'use strict';
  angular
    .module('su-cadastro-imovel')
    .controller('HomologarImovelController',  ["$rootScope", "$scope", "$mdDialog", "Homologado", "$state", "imovelResolve", "ExibirPlantaUnidade", "ExibirCertidao", "ExibirPlantaBenfeitoria", "fileService", "CartografiaServiceInc", "MensagensService", "ArquivoService", "ciMapaLayersFactory", "ciMapaViewFactory", "CartorioService", "homologarEnviarDestinacaoService", "$anchorScroll", "$location", function (
      $rootScope,
      $scope,
      $mdDialog,
      Homologado,
      $state,
      imovelResolve,
      ExibirPlantaUnidade,
      ExibirCertidao,
      ExibirPlantaBenfeitoria,
      fileService,
      CartografiaServiceInc,
      MensagensService,
      ArquivoService,
      ciMapaLayersFactory,
      ciMapaViewFactory,
      CartorioService,
      homologarEnviarDestinacaoService,
      $anchorScroll,
      $location
    ) {
      var vm = this,
        WGS84_WEB_MERCATOR = 3857;

      vm.imovelOriginal = imovelResolve.imovelOriginal;
      vm.imovelAlterado = imovelResolve.imovelAlterado;
      vm.todosImoveisDaUf = imovelResolve.todosImoveisDaUf;

      ciMapaLayersFactory.init(true);
      ciMapaViewFactory.init();

      vm.CONSTANTES_IMOVEL_RECUSADO = {
        "TIPO_IMOVEL": 'tipoImovel',
        "MAPA_IMOVEL": 'mapaImovel',
        "COORDENADAS_PONTO": 'coordenadasPonto',
        "ENDERECO": 'endereco',
        "DETALHES_IMOVEL": 'detalhesImovel',
        "DADOS_INCORPORACAO": 'dadosIncorporacao',
        "FOTOS_IMOVEL_GERAL": 'fotosImovelGeral',
        "DADOS_CARTORIAIS": 'dadosCartoriais',
        "DADOS_CADASTRAIS": 'dadosCadastrais',
        "RESTRICOES_USO_OCUPACAO": 'restricoesUsoOcupacao',
        "ARQUIVOS_COMPLEMENTARES_TERRENO": 'arquivosComplementaresTerreno',
        "PROPRIETARIO_TITULAR": 'proprietarioTitular',
        "PROPRIETARIO_ANTERIOR": 'proprietarioAnterior',
        "UNIDADE_AUTONOMA": 'unidadeAutonoma',
        "UNIDADE_AUTONOMA_PLANTAS": 'unidadeAutonomaPlantas',
        "FOTOS_UNIDADE_AUTONOMA": 'fotosUnidadeAutonoma',
        "UNIDADE_AUTONOMA_EDIFICACAO": 'unidadeAutonomaEdificacao',
        "UNIDADE_AUTONOMA_ACESSIBILIDADE": 'unidadeAutonomaAcessibilidade',
        "UNIDADE_AUTONOMA_EDIFICACAO_DADOS_MEDIDOR": 'unidadeAutonomaEdificacaoDadosMedidor',
        "UNIDADE_AUTONOMA_EDIFICACAO_DOCUMENTACAO": 'unidadeAutonomaEdificacaoDocumentacao',
        "MATRICULA_TRANSCRICAO": 'matriculaTranscricao',
        "DOCUMENTO_CARTORIAL": 'documentoCartorial',
      }

      vm.coordenadasImovelOriginal = null;
      vm.coordenadasImovelAlterado = null;
      vm.isBenfeitoria = false;
      vm.homologarImovelForm = {};
      //vm.imovelRecusado = null;
      vm.isDetalhamento = false;
      vm.tituloPagina = '';
      vm.titulosAquisitivos = [];
      vm.matriculas = [];
      vm.benfeitorias = [];
      vm.exibeDadosLocalizacao = true;

      function init() {
        vm.coordenadasImovelOriginal = infoCoordenadas();
        vm.coordenadasImovelAlterado = infoCoordenadas();
        vm.isBenfeitoria =
          vm.imovelOriginal.tipoImovel.id === 2 ||
          vm.imovelOriginal.tipoImovel.id === 3;

        if (vm.imovelAlterado.sistemaReferencia) {
          if (vm.imovelAlterado.sistemaReferencia.codigo) {
            atualizarInformacoesCoordenadas(
              vm.coordenadasImovelAlterado,
              vm.imovelAlterado.sistemaReferencia.codigo,
              vm.imovelAlterado.pontoImovel.coordinates
            );

            if (
              vm.isCadastroAlterado() &&
              vm.imovelOriginal.sistemaReferencia.codigo
            ) {
              atualizarInformacoesCoordenadas(
                vm.coordenadasImovelOriginal,
                vm.imovelOriginal.sistemaReferencia.codigo,
                vm.imovelOriginal.pontoImovel.coordinates
              );
            }
          }
        }

        for (var i = 0; i < vm.imovelOriginal.incorporacao.matriculas.length; i++) {
          vm.imovelOriginal.incorporacao.matriculas[i].cartorio = {};
          vm.imovelOriginal.incorporacao.matriculas[i].cartorio.nome = '...';
          preparaCartorio(vm.imovelOriginal, vm.imovelOriginal.incorporacao.matriculas[i].cartorioID, i);
        }

        for (var i = 0; i < vm.imovelAlterado.incorporacao.matriculas.length; i++) {
          vm.imovelAlterado.incorporacao.matriculas[i].cartorio = {};
          vm.imovelAlterado.incorporacao.matriculas[i].cartorio.nome = '...';
          preparaCartorio(vm.imovelAlterado, vm.imovelAlterado.incorporacao.matriculas[i].cartorioID, i);
        }

        if (vm.imovelOriginal.incorporacao.certidoesCartoriais.length > 0) {
          for (var i = 0; i < vm.imovelOriginal.incorporacao.certidoesCartoriais.length; i++) {
            preparaCartorioCertidoes(vm.imovelOriginal, vm.imovelOriginal.incorporacao.certidoesCartoriais[i].cartorio, i);
          }
        }

        if (vm.imovelAlterado.incorporacao.certidoesCartoriais.length > 0) {
          for (var i = 0; i < vm.imovelAlterado.incorporacao.certidoesCartoriais.length; i++) {
            preparaCartorioCertidoes(vm.imovelAlterado, vm.imovelAlterado.incorporacao.certidoesCartoriais[i].cartorio, i);
          }
        }

        inicializaImovelRecusado();
        iniciaObjetoImovelRecusado();
        gerarUrlArquivosComplementares();
        gerarUrlTitulos();
        gerarUrlDocumentoCadastralTerreno();
        gerarUrlDocumentoCartorialTerreno();
        gerarUrlDocumentosCartoriais();
        gerarUrlDocumentosUnidadeAutonoma();
        gerarUrlPlantasUnidadeAutonoma();
        extrairTitulosAquisitivos();
        extrairMatriculas();
        extrairBenfeitorias();
        gerarUrlDocumentosBenfeitoria();
        gerarUrlPlantasBenfeitoria();
      }

      function preparaCartorio(model, cartorioID, index) {
        CartorioService.pesquisaCartorioPorId
          .query({ id: cartorioID })
          .$promise.then(function (data) {
            for (var i = 0; i < model.incorporacao.matriculas.length; i++) {
              if (i == index) {
                model.incorporacao.matriculas[i].cartorio = data;
                return;
              }
            }
          });
      }

      function preparaCartorioCertidoes(model, cartorioID, index) {
        CartorioService.pesquisaCartorioPorId
          .query({ id: cartorioID })
          .$promise.then(function (data) {
            for (var i = 0; i < model.incorporacao.certidoesCartoriais.length; i++) {
              if (i == index) {
                model.incorporacao.certidoesCartoriais[i].cartorioRecuperado = data;
                return;
              }
            }
          });
      }

      vm.isCadastroAlterado = function () {
        return !!vm.imovelOriginal.rip;
      };

      function extrairBenfeitorias() {
        if (vm.isBenfeitoria) {
            if(vm.imovelOriginal.benfeitorias != null && vm.imovelOriginal.benfeitorias != undefined) {
                for (var i = 0; i < vm.imovelOriginal.benfeitorias.length; i++) {
                    var codigo = vm.imovelOriginal.benfeitorias[i].codigo;

                    var benfeitoria = {
                        benfeitoriaOriginal: vm.imovelOriginal.benfeitorias[i],
                        benfeitoriaAlterada: getByAttribute(
                            vm.imovelAlterado.benfeitorias,
                            'codigo',
                            codigo
                        )
                    };

                    vm.benfeitorias.push(benfeitoria);
                }
            }
            if(vm.imovelAlterado.benfeitorias != null && vm.imovelAlterado.benfeitorias != undefined) {
                for (i = 0; i < vm.imovelAlterado.benfeitorias.length; i++) {
                    benfeitoria = {
                        benfeitoriaOriginal: null,
                        benfeitoriaAlterada: vm.imovelAlterado.benfeitorias[i]
                    };
                    codigo = vm.imovelAlterado.benfeitorias[i].codigo;

                    if (!contem(vm.imovelOriginal.benfeitorias, 'codigo', codigo)) {
                        vm.benfeitorias.push(benfeitoria);
                    }
                }
            }
        }
      }

      function extrairTitulosAquisitivos() {
        for (var i = 0; i < vm.imovelOriginal.titulos.length; i++) {
          var nome = vm.imovelOriginal.titulos[i].nome;

          var tituloAquisitivo = {
            tituloOriginal: vm.imovelOriginal.titulos[i],
            tituloAlterado: getByAttribute(
              vm.imovelAlterado.titulos,
              'nome',
              nome
            )
          };

          vm.titulosAquisitivos.push(tituloAquisitivo);
        }

        for (i = 0; i < vm.imovelAlterado.titulos.length; i++) {
          tituloAquisitivo = {
            tituloOriginal: null,
            tituloAlterado: vm.imovelAlterado.titulos[i]
          };

          nome = vm.imovelAlterado.titulos[i].nome;

          if (!contem(vm.imovelOriginal.titulos, 'nome', nome)) {
            vm.titulosAquisitivos.push(tituloAquisitivo);
          }
        }
      }

      function extrairMatriculas() {
        for (
          var i = 0;
          i < vm.imovelOriginal.incorporacao.matriculas.length;
          i++
        ) {
          var numero = vm.imovelOriginal.incorporacao.matriculas[i].numero;

          var matricula = {
            matriculaOriginal: vm.imovelOriginal.incorporacao.matriculas[i],
            matriculaAlterada: getByAttribute(
              vm.imovelAlterado.incorporacao.matriculas,
              'numero',
              numero
            )
          };

          vm.matriculas.push(matricula);
        }

        for (i = 0; i < vm.imovelAlterado.incorporacao.matriculas.length; i++) {
          matricula = {
            matriculaOriginal: null,
            matriculaAlterada: vm.imovelAlterado.incorporacao.matriculas[i]
          };

          numero = vm.imovelAlterado.incorporacao.matriculas[i].numero;

          if (
            !contem(vm.imovelOriginal.incorporacao.matriculas, 'numero', numero)
          ) {
            vm.matriculas.push(matricula);
          }
        }
      }

      function getByAttribute(objectArray, attribute, item) {
        var retorno = null;

        for (var i = 0; i < objectArray.length; i++) {
          if (item === objectArray[i][attribute]) {
            retorno = objectArray[i];
            break;
          }
        }

        return retorno;
      }

      function contem(objectArray, attribute, item) {
        var retorno = false;
          if (objectArray != null && objectArray != undefined) {
              for (var i = 0; i < objectArray.length; i++) {
                  if (item === objectArray[i][attribute]) {
                      retorno = true;
                  }
              }
          }
        return retorno;
      }

      function infoCoordenadas() {
        return {
          latitude: {
            hemisferio: null,
            graus: null,
            minutos: null,
            segundos: null,
            decimal: null
          },
          longitude: {
            hemisferio: null,
            graus: null,
            minutos: null,
            segundos: null,
            decimal: null
          },
          coordenadaE: null,
          coordenadaN: null,
          fusoUTM: null
        };
      }

      function atualizarInformacoesCoordenadas(
        model,
        sistemaReferencia,
        coordenadas
      ) {
        CartografiaServiceInc.transformarGeometria(
          WGS84_WEB_MERCATOR,
          sistemaReferencia,
          coordenadas[0],
          coordenadas[1]
        ).then(function (result) {
          model.longitude.decimal = result.data.coordinates[0];
          model.longitude.hemisferio = model.longitude.decimal < 0 ? 'O' : 'L';

          model.latitude.decimal = result.data.coordinates[1];
          model.latitude.hemisferio = model.latitude.decimal < 0 ? 'S' : 'N';

          var grauDecimal = {
            longitude: {
              decimal: model.longitude.decimal,
              hemisferio: model.longitude.hemisferio
            },
            latitude: {
              decimal: model.latitude.decimal,
              hemisferio: model.latitude.hemisferio
            }
          };

          var gms = CartografiaServiceInc.gdToGms(grauDecimal);

          model.longitude.graus = gms.longitude.graus;
          model.longitude.minutos = gms.longitude.minutos;
          model.longitude.segundos = gms.longitude.segundos;

          model.latitude.graus = gms.latitude.graus;
          model.latitude.minutos = gms.latitude.minutos;
          model.latitude.segundos = gms.latitude.segundos;

          var utm = CartografiaServiceInc.gdToUtm(grauDecimal);

          model.coordenadaE = utm.coordenadaE;
          model.coordenadaN = utm.coordenadaN;
          model.fusoUTM = utm.fusoUTM;
        });
      }

      vm.isDifferent = function (div) {

        if (!vm.isCadastroAlterado()) {
          return false;
        }

        if (vm.isDetalhamento) {
          return true;
        }

        var diferencas = angular.element(div).find('span.ng-binding.span-alterado-component');

        if (angular.element(div)[0].childElementCount === 0) {
          return false;
        }
        if (diferencas.length !== 0)
          return false;
        else
          return true;

      }

      vm.toggle = function (value) {
        var idx = vm.imovelRecusado.camposRecusados.indexOf(value);

        if (idx > -1) {
          vm.informacoesRecusada.checkAll = false;
          vm.imovelRecusado.camposRecusados.splice(idx, 1);
        } else {
          vm.imovelRecusado.camposRecusados.push(value);
        }
        if (vm.imovelRecusado.camposRecusados.length == 22) {
          vm.informacoesRecusada.checkAll = true;
        }
      };

      vm.toggleAll = function () {

        // INFORMAR TODOS OS NG-MODEL DE CADA CHECKBOX DA TELA DE HOMOLOGACAO SEGUINDO MESMO PADRAO
        vm.informacoesRecusada.tipoImovel = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.mapaImovel = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.coordenadasPonto = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.endereco = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.detalhesImovel = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.dadosIncorporacao = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.fotosImovelGeral = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.dadosCartoriais = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.dadosCadastrais = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.restricoesUsoOcupacao = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.arquivosComplementaresTerreno = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.proprietarioTitular = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.proprietarioAnterior = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonoma = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.fotosUnidadeAutonoma = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonomaPlantas = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonomaEdificacao = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonomaAcessibilidade = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonomaEdificacaoDadosMedidor = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.unidadeAutonomaEdificacaoDocumentacao = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.matriculaTranscricao = vm.informacoesRecusada.checkAll;
        vm.informacoesRecusada.documentoCartorial = vm.informacoesRecusada.checkAll;

        //CASO ELE CLICK EM RECUSAR TODOS OS DADOS O OBJETO CAMPOS RECUSADOS E LIMPO E DEPOIS ADD TODOS PARA EVITAR
        //DUPLICIDADE NA LISTA
        if (vm.informacoesRecusada.checkAll) {
          vm.imovelRecusado.camposRecusados = [];

          angular.forEach(Object.values(vm.CONSTANTES_IMOVEL_RECUSADO), function (value) {
            vm.imovelRecusado.camposRecusados.push(value);
          });
        } else {
          vm.imovelRecusado.camposRecusados = [];
        }

      };

      vm.formAdress = function (imovel) {
        var endereco = '';

        if (imovel.enderecamento.pais.id !== 1) {
          endereco = imovel.enderecamento.endereco + ', ' + imovel.enderecamento.cidadeExterior + ' - ' + imovel.enderecamento.pais.descricao + ', ' + imovel.enderecamento.codigoPostal;
          return endereco;
        }

        if (imovel.enderecamento.tipoLogradouro) {
          endereco += imovel.enderecamento.tipoLogradouro + ' ';
        }
        if (imovel.enderecamento.logradouro) {
          endereco += imovel.enderecamento.logradouro + ', ';
        }
        if (imovel.numero) {
          endereco += 'Número: ' + imovel.numero + ', ';
        }
        if (imovel.enderecamento.bairro) {
          endereco += imovel.enderecamento.bairro + ', ';
        }
        if (imovel.complemento) {
          endereco += imovel.complemento + ', ';
        }
        if (imovel.enderecamento.municipio) {
          endereco += imovel.enderecamento.municipio + '/' + imovel.enderecamento.uf + ' ';
        }
        if (imovel.enderecamento.pais) {
          endereco += imovel.enderecamento.pais.descricao + ', ';
        }
        if (imovel.enderecamento.cep) {
          endereco += 'CEP: ' + imovel.enderecamento.cep;
        }
        return endereco;


      }

      vm.sizeColspan = function () {
        if (vm.isCadastroAlterado()) {
          return 2;
        }
        return 1;
      }

      vm.sizeColspanFull = function () {
        if (vm.isCadastroAlterado()) {
          return 3;
        }
        return 2;
      }

      vm.isAllChecked = function () {
        return (
          vm.imovelRecusado.camposRecusados.length > 0 &&
          vm.imovelRecusado.camposRecusados.length ===
          angular
            .element('#detalhamentoImovel')
            .find('md-checkbox:not([disabled])').length
        );
      };

      vm.isIndeterminate = function () {
        return (
          vm.imovelRecusado.camposRecusados.length > 0 && !vm.isAllChecked()
        );
      };

      vm.exists = function (campo) {
        return vm.imovelRecusado.camposRecusados.indexOf(campo) > -1;
      };

      vm.desabilitarCheckbox = function (dadoOriginal, dadoAlterado) {
        return (
          vm.isDetalhamento ||
          (vm.isCadastroAlterado() && vm.isEquals(dadoOriginal, dadoAlterado))
        );
      };

      vm.isEquals = function (dadoOriginal, dadoAlterado) {
        if (dadoAlterado instanceof Object) {
          return angular.equals(dadoOriginal, dadoAlterado);
        } else {
          return dadoOriginal == dadoAlterado;
        }
      };

      vm.isPaisBrasil = function () {
        return (
          vm.imovelAlterado.enderecamento.pais &&
          vm.imovelAlterado.enderecamento.pais.id === 1
        );
      };

      vm.isDadoDivergente = function (dadoOriginal, dadoAlterado) {
        return (
          vm.isCadastroAlterado() && !angular.equals(dadoOriginal, dadoAlterado)
        );
      };

      vm.recusarImovel = function () {
        if (
          vm.imovelRecusado.camposRecusados.length > 0 &&
          vm.homologarImovelForm.$valid
        ) {
          var confirm = $mdDialog.confirm({
            focusOnOpen: false,
            textContent: 'Deseja recusar este imóvel?',
            cancel: 'Não',
            ok: 'Recusar'
          });

          $mdDialog.show(confirm).then(function () {
            Homologado.recusar(vm.imovelRecusado).then(function () {
              $state.go('INCORPORACAO.homologarImovel');
              MensagensService.exibirMensagemSucesso(
                'Imóvel recusado com sucesso.'
              );
            });
          });
        } else if (vm.imovelRecusado.camposRecusados.length === 0) {
          MensagensService.exibirMensagemErro(
            'É necessário selecionar a opção recusar de pelo menos um dos campos.'
          );
        } else {
          MensagensService.exibirMensagemCamposObrigatorios();
        }
      };

      vm.fechar = function () {
        if (vm.isDetalhamento && !!$state.previousState.name) {
          $state.go($state.previousState.name, $state.previousState.params);
        } else {
          $state.go('INCORPORACAO.homologarImovel');
        }
      };

      vm.disableHomologacao = function () {
        return vm.imovelRecusado.camposRecusados.length > 0;
      };

      function gerarUrlArquivosComplementares() {
        angular.forEach(vm.imovelAlterado.arquivosComplementaresLista, function (
          arquivoComplementar
        ) {
          arquivoComplementar.arquivo.url = ArquivoService.getUrlFromBase64(
            arquivoComplementar.arquivo.arquivo
          );
        });

        angular.forEach(vm.imovelOriginal.arquivosComplementaresLista, function (
          arquivoComplementar
        ) {
          arquivoComplementar.arquivo.url = ArquivoService.getUrlFromBase64(
            arquivoComplementar.arquivo.arquivo
          );
        });
      }

      function gerarUrlDocumentosCartoriais() {
        angular.forEach(
          vm.imovelAlterado.incorporacao.certidoesCartoriais,
          function (documento) {
            documento.arquivo.url = ArquivoService.getUrlFromBase64(
              documento.arquivo.arquivo
            );
          }
        );

        angular.forEach(
          vm.imovelOriginal.incorporacao.certidoesCartoriais,
          function (documento) {
            documento.arquivo.url = ArquivoService.getUrlFromBase64(
              documento.arquivo.arquivo
            );
          }
        );
      }

      function gerarUrlDocumentoCartorialTerreno() {
        if (vm.imovelAlterado.terreno.arquivoCartorial) {
          vm.imovelAlterado.terreno.arquivoCartorial.url = ArquivoService.getUrlFromBase64(
            vm.imovelAlterado.terreno.arquivoCartorial.arquivo);
        }

        if (vm.imovelOriginal.terreno.arquivoCartorial) {
          vm.imovelOriginal.terreno.arquivoCartorial.url = ArquivoService.getUrlFromBase64(
            vm.imovelOriginal.terreno.arquivoCartorial.arquivo);
        }
      }

      function gerarUrlDocumentoCadastralTerreno() {
        if (vm.imovelAlterado.terreno.arquivoCadastral) {
          vm.imovelAlterado.terreno.arquivoCadastral.url = ArquivoService.getUrlFromBase64(
            vm.imovelAlterado.terreno.arquivoCadastral.arquivo);
        }

        if (vm.imovelOriginal.terreno.arquivoCadastral) {
          vm.imovelOriginal.terreno.arquivoCadastral.url = ArquivoService.getUrlFromBase64(
            vm.imovelOriginal.terreno.arquivoCadastral.arquivo);
        }
      }

      function gerarUrlTitulos() {
        angular.forEach(
          vm.imovelAlterado.titulos,
          function (documento) {
            documento.url = ArquivoService.getUrlFromBase64(
              documento.arquivo
            );
          }
        );

        angular.forEach(
          vm.imovelOriginal.titulos,
          function (documento) {
            documento.url = ArquivoService.getUrlFromBase64(
              documento.arquivo
            );
          }
        );
      }

        function gerarUrlDocumentosBenfeitoria() {
            if (vm.benfeitorias.length !== 0) {
                angular.forEach(vm.benfeitorias, function (benfeitoria) {
                    if (benfeitoria.benfeitoriaAlterada && benfeitoria.benfeitoriaAlterada.edificacao) {
                        angular.forEach(
                            benfeitoria.benfeitoriaAlterada.edificacao.documentacao,
                            function (documento) {
                                if (documento.arquivo) {
                                    documento.arquivo.url = ArquivoService.getUrlFromBase64(
                                        documento.arquivo.arquivo
                                    );
                                }
                            }
                        );
                    }

                    if (benfeitoria.benfeitoriaOriginal && benfeitoria.benfeitoriaOriginal.edificacao) {
                        angular.forEach(
                            benfeitoria.benfeitoriaOriginal.edificacao.documentacao,
                            function (documento) {
                                if (documento.arquivo) {
                                    documento.arquivo.url = ArquivoService.getUrlFromBase64(
                                        documento.arquivo.arquivo
                                    );
                                }
                            }
                        );
                    }
                });
            }
        }

      function gerarUrlPlantasBenfeitoria() {
        if (vm.benfeitorias.length !== 0) {
          angular.forEach(vm.benfeitorias, function (benfeitoria) {
           if(benfeitoria.benfeitoriaAlterada) {
               angular.forEach(
                   benfeitoria.benfeitoriaAlterada.plantas,
                   function (plantaArq) {
                       if (plantaArq.planta) {
                           plantaArq.url = ArquivoService.getUrlFromBase64(
                               plantaArq.planta
                           );
                       }
                   }
               );
           }
              if (benfeitoria.benfeitoriaOriginal) {
                  angular.forEach(
                      benfeitoria.benfeitoriaOriginal.plantas,
                      function (plantaArq) {
                          if (plantaArq.planta) {
                              plantaArq.url = ArquivoService.getUrlFromBase64(
                                  plantaArq.planta
                              );
                          }
                      }
                  );
              }
          });
        }
      }

      function gerarUrlDocumentosUnidadeAutonoma() {
        if (
          vm.imovelAlterado.unidadeAutonoma &&
          vm.imovelAlterado.unidadeAutonoma.edificacao
        ) {
          angular.forEach(
            vm.imovelAlterado.unidadeAutonoma.edificacao.documentacao,
            function (documento) {
              if (documento.arquivo) {
                documento.arquivo.url = ArquivoService.getUrlFromBase64(
                  documento.arquivo.arquivo
                );
              }
            }
          );

          angular.forEach(
            vm.imovelOriginal.unidadeAutonoma.edificacao.documentacao,
            function (documento) {
              if (documento.arquivo) {
                documento.arquivo.url = ArquivoService.getUrlFromBase64(
                  documento.arquivo.arquivo
                );
              }
            }
          );
        }
      }

      function gerarUrlPlantasUnidadeAutonoma() {
        if (vm.imovelAlterado.unidadeAutonoma) {
          angular.forEach(vm.imovelAlterado.unidadeAutonoma.plantas, function (
            planta
          ) {
            planta.arquivo.url = ArquivoService.getUrlFromBase64(
              planta.arquivo.arquivo
            );
          });

          angular.forEach(vm.imovelOriginal.unidadeAutonoma.plantas, function (
            planta
          ) {
            planta.arquivo.url = ArquivoService.getUrlFromBase64(
              planta.arquivo.arquivo
            );
          });
        }
      }

      function iniciaObjetoImovelRecusado() {
        vm.informacoesRecusada = {};
        vm.informacoesRecusada.tipoImovel = false;
        vm.informacoesRecusada.checkAll = false;
        vm.informacoesRecusada.mapaImovel = false;
        vm.informacoesRecusada.coordenadasPonto = false;
        vm.informacoesRecusada.endereco = false;
        vm.informacoesRecusada.detalhesImovel = false;
        vm.informacoesRecusada.dadosIncorporacao = false;
        vm.informacoesRecusada.fotosImovelGeral = false;
        vm.informacoesRecusada.dadosCartoriais = false;
        vm.informacoesRecusada.dadosCadastrais = false;
        vm.informacoesRecusada.restricoesUsoOcupacao = false;
        vm.informacoesRecusada.arquivosComplementaresTerreno = false;
        vm.informacoesRecusada.proprietarioTitular = false;
        vm.informacoesRecusada.proprietarioAnterior = false;
        vm.informacoesRecusada.unidadeAutonoma = false;
        vm.informacoesRecusada.unidadeAutonomaPlantas = false;
        vm.informacoesRecusada.fotosUnidadeAutonoma = false;
        vm.informacoesRecusada.unidadeAutonomaEdificacao = false;
        vm.informacoesRecusada.unidadeAutonomaAcessibilidade = false;
        vm.informacoesRecusada.unidadeAutonomaEdificacaoDadosMedidor = false;
        vm.informacoesRecusada.unidadeAutonomaEdificacaoDocumentacao = false;
        vm.informacoesRecusada.matriculaTranscricao = false;
        vm.informacoesRecusada.documentoCartorial = false;
      }

      function inicializaImovelRecusado() {
        if ($state.current.name === 'INCORPORACAO.detalhesHomologacao') {
          if (vm.isCadastroAlterado()) {
            vm.imovelRecusado = vm.imovelAlterado.imovelRecusado;
          } else {
            vm.imovelRecusado = vm.imovelOriginal.imovelRecusado;
          }

          vm.isDetalhamento = true;
          vm.tituloPagina = 'Detalhar Homologação do Imóvel';
        } else {
          vm.imovelRecusado = {
            imovel: vm.imovelAlterado,
            observacao: null,
            camposRecusados: []
          };
          vm.tituloPagina = 'Homologar Imóvel';
        }
      }

      vm.boolToString = function (boolean) {
        if (angular.isDefined(boolean)) {
          return boolean ? 'Sim' : 'Não';
        }
      };

      vm.showHomologarImovel = function (ev) {
        var confirm = $mdDialog.confirm({
          focusOnOpen: false,
          textContent: 'Deseja realmente homologar este imóvel?',
          targetEvent: ev,
          cancel: 'Não',
          ok: 'Homologar'
        });

        if (
          $rootScope.usuarioLogado.jurisdicoes.includes(
            vm.imovelAlterado.enderecamento.uf
          )
        ) {
          $mdDialog.show(confirm).then(
            function () {
              $mdDialog.show(
                $mdDialog.alert({
                  templateUrl:
                    'scripts-cadastro-imovel/pages/imovel/view/modals/progress.tmpl.html'
                })
              );
              vm.imovelAlterado.imovelRecusado = null;

              Homologado.save(vm.imovelAlterado).then(function (data) {
                MensagensService.exibirMensagemSucesso(
                  'Imóvel homologado com sucesso Rip: ' + data.rip
                );
                vm.imovelOriginal.rip = data.rip;
                $mdDialog.hide();
                vm.enviarDocumentosSei();
              });
            },
            function () {
              $mdDialog.cancel();
            }
          );
        } else {
          MensagensService.exibirMensagemErro(
            'O imóvel deve estar contido nas jurisdições do usuário.'
          );
        }
      };

      vm.goToDadosLocalizacao = function () {
        $anchorScroll();
        $location.hash('dadosDoImovel');
        vm.exibeDadosLocalizacao = true;
      };

        vm.enviarDocumentosSei = function () {
            $mdDialog.show({
                controller: enviarDocumentosSeiDialogController,
                templateUrl:
                    'scripts-cadastro-imovel/pages/imovel/view/modals/dialog-enviar-documentos-sei.html',
                parent: angular.element(document.body),
                clickOutsideToClose: false,
                locals: {
                    imovel: vm.imovelOriginal
                }
            });
        };

        var enviarDocumentosSeiDialogController = enviarDocumentosSeiDialogController;

        enviarDocumentosSeiDialogController.$inject = [
            '$scope',
            '$http',
            '$mdDialog',
            'imovel',
            '$timeout'
        ];
        function enviarDocumentosSeiDialogController(
            $scope,
            $http,
            $mdDialog,
            imovel,
            $timeout
        ) {
            $scope.imovel = imovel;
            $scope.envioSei = {};
            $scope.envioSei.despachoConclusao = false;
            $scope.envioSei.folhaRosto = false;
            $scope.envioSei.memorialDescritivo = false;
            $scope.envioSei.shapefileTerrenoCartorial = false;
            $scope.envioSei.shapefileTerrenoCadastral = false;
            $scope.envioSei.shapefileBenfeitorias = false;
            $scope.envioSei.shapefileEdificacao = false;
            $scope.envioSei.tituloAquisitivo = false;
            $scope.envioSei.documentoCartorial = false;
            $scope.envioSei.arquivosComplementares = false;
            $scope.envioSei.documentacaoEdificacao = false;
            $scope.envioSei.arquivoPlanta = false;
            $scope.showEdificacaoAutonoma = false;
            $scope.showEdificacaoBenfeitoria = false;
            $scope.showPlantaBenfeitoria = false;
            $scope.showPlantaAutonoma = false;

            apresentaDocumentacaoEdificacao();
            apresentaDocumentacaoBenfeitoria();
            apresentaPlantasBenfeitoria();
            apresentaPlantaEdificacao();

            $scope.fechar = function () {
                $timeout(function () {
                    $state.go('INCORPORACAO.homologarImovel');
                }, 1000);
                $mdDialog.cancel();
            };

            function apresentaDocumentacaoBenfeitoria () {
                if (imovel.benfeitorias != null && imovel.benfeitorias != undefined) {
                    angular.forEach(imovel.benfeitorias, function (benfeitoria) {
                        if (benfeitoria.edificacao != null && benfeitoria.edificacao != undefined) {
                            angular.forEach(benfeitoria.edificacao.documentacao, function (documento) {
                                if (documento.arquivo != null && documento.arquivo != undefined) {
                                    $scope.showEdificacaoBenfeitoria = true;
                                }
                            });
                        }
                    });
                }
            }


            function apresentaDocumentacaoEdificacao () {
                if (imovel.unidadeAutonoma != null && imovel.unidadeAutonoma != undefined) {
                    angular.forEach(imovel.unidadeAutonoma.edificacao.documentacao, function (value) {
                        if (value.arquivo != null && value.arquivo != undefined) {
                            $scope.showEdificacaoAutonoma = true;
                        }
                    });
                }
            }

            function apresentaPlantaEdificacao () {
                if (imovel.unidadeAutonoma != null && imovel.unidadeAutonoma != undefined) {
                    angular.forEach(imovel.unidadeAutonoma.edificacao.plantas, function (value) {
                        if (value.arquivo != null && value.arquivo != undefined) {
                            $scope.showPlantaAutonoma = true;
                        }
                    });
                }
            }

            function apresentaPlantasBenfeitoria () {
                if (imovel.benfeitorias != null && imovel.benfeitorias != undefined) {
                    angular.forEach(imovel.benfeitorias, function (benfeitoria) {
                        if (benfeitoria.plantas != null && benfeitoria.plantas != undefined) {
                            angular.forEach(benfeitoria.plantas, function (planta) {
                                if (planta.planta != null && planta.planta != undefined) {
                                    $scope.showPlantaBenfeitoria = true;
                                }
                            });
                        }
                    });
                }
            }

            $scope.confirmar = function () {
                $scope.envioSei.imovel = $scope.imovel;
                homologarEnviarDestinacaoService.documentoSei($scope.envioSei).then(function () {
                    $mdDialog.cancel();
                    MensagensService.exibirMensagemSucesso("Arquivos enviados para o SEI com sucesso");
                    $timeout(function () {
                        $state.go('INCORPORACAO.homologarImovel');
                    }, 1000);
                },function (erro){
                    MensagensService.exibirMensagemErro(erro);
                });
            };
        }
      init();

    }]);
})();

(function () {
  'use strict';

  consultarHomologarController.$inject = ["$scope", "$mdDialog", "Dominio", "ectService", "ectCepService", "UfsService", "PaisesUtilizadosService", "MunicipioService", "ImovelPesquisa", "TipoCancelImovelPesquisa", "SituacaoImovel", "Cancelamento", "HomologacaoImovelService", "SituacaoCadastro", "MAPA_CONSTANTS_INCORPORACAO", "NotificadorService", "MensagensService", "$state", "PaginacaoFactory", "camposAjuda", "escopoCompartilhadoService", "$document", "AcaoExecutasService", "CpfCnpjService", "CidadesExtService"];
  angular
    .module('su-cadastro-imovel')
    .controller('ConsultarHomologarController', consultarHomologarController);

  function consultarHomologarController(
    $scope,
    $mdDialog,
    Dominio,
    ectService,
    ectCepService,
    UfsService,
    PaisesUtilizadosService,
    MunicipioService,
    ImovelPesquisa,
    TipoCancelImovelPesquisa,
    SituacaoImovel,
    Cancelamento,
    HomologacaoImovelService,
    SituacaoCadastro,
    MAPA_CONSTANTS_INCORPORACAO,
    NotificadorService,
    MensagensService,
    $state,
    PaginacaoFactory,
    camposAjuda,
    escopoCompartilhadoService,
    $document,
    AcaoExecutasService,
    CpfCnpjService,
    CidadesExtService
  ) {
    var vm = this,
      notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES;

    vm.imovel = {};
    vm.imovelSelecionado = {};
    vm.situacoes = SituacaoImovel.query();
    vm.resultadoPesquisa = [];
    vm.motivos = TipoCancelImovelPesquisa.query();
    vm.tiposTitular = Dominio.tiposProprietarioReal();
    AcaoExecutasService.query().$promise.then(function (result) {
      vm.acoesExecutadas = [];
      for (var i = 0; i <= result.length; i++) {
        if (result[i] != undefined && result[i].name !== "HOMOLOGADO" && result[i].name !== "CANCELAMENTO_IMOVEL") {
          vm.acoesExecutadas.push(result[i]);
        }

      }
    });
    vm.paises = PaisesUtilizadosService.query();
    vm.situacaoCadastro = SituacaoCadastro.query();
    vm.ufs = UfsService.UFS;
    vm.municipios = [];
    vm.Cancelamento = Cancelamento;
    vm.imovelPesquisa = { enderecamento: {} };
    vm.paginacao = PaginacaoFactory.getDefault();
    vm.cepPreenchido = false;
    vm.cepValido = false;
    vm.isCepGenerico = false;
    vm.imovelPesquisa.cpfCnpjProprietarios = [];
    vm.exibirImoveisCadastradosEditadosDoUsuario = false;
    vm.cidades = [];

    function init() {
      escopoCompartilhadoService.setObjetos('hints', camposAjuda);

      if (!$state.previousState.name) {
        pesquisar();
      }
    }

    vm.progress = function () {
      $mdDialog.show(
        $mdDialog.alert({
          templateUrl:
            'scripts-cadastro-imovel/pages/imovel/view/modals/progress.tmpl.html'
        })
      );
    }

    vm.closeProgress = function () {
      $mdDialog.hide();
    }

    vm.tratamentoRip = function () {
      vm.imovelPesquisa.rip = !vm.imovelPesquisa.rip || !vm.imovelPesquisa.rip.replaceAll("0", "") ?
        undefined : ('00000000' + vm.imovelPesquisa.rip).slice(-8);
      return vm.imovelPesquisa.rip
    }

    vm.buscaEnderecoPorCep = function () {
      var cep = vm.imovelPesquisa.cep;
      if (cep != undefined && cep.length == 8) {
        vm.progress();
        ectCepService.get({ cep: cep }).$promise.then(
          function (data) {
            data.pais = Dominio.Constantes.Brasil;
            vm.imovelPesquisa.enderecamento = data;
            vm.imovelPesquisa.uf = vm.imovelPesquisa.enderecamento.uf;
            vm.imovelPesquisa.municipio = vm.imovelPesquisa.enderecamento.municipio;
            $scope.$emit('municipioImovelSelecionado', data.codigoIbge);
            vm.isCepGenerico = vm.verificaCEPGenerico(cep);
            vm.cepValido = true;
            vm.closeProgress();
          },
          function () {
            vm.closeProgress();
            vm.imovelPesquisa.uf = '';
            vm.imovelPesquisa.municipio = '';
            vm.cepValido = false;
            MensagensService.exibirMensagemErro('CEP não encontrado');
          }
        );
        vm.cepPreenchido = true;
      } else {
        vm.imovelPesquisa.enderecamento = {};
        vm.imovelPesquisa.enderecamento.pais = Dominio.Constantes.Brasil;
        vm.cepPreenchido = false;
        vm.cepValido = false;
      }
    };

    vm.verificaCEPGenerico = function (cep) {
      return cep.endsWith('000');
    };

    function pesquisar() {
      vm.resultadoPesquisa = [];
      var imovelSerializado = vm.imovelPesquisa;
      delete imovelSerializado.enderecamento.geometria;
      HomologacaoImovelService.buscarTodos(
        imovelSerializado,
        vm.paginacao.page,
        vm.paginacao.limit
      ).then(function (result) {
        if (result.data.content.length > 0) {
          vm.paginacao.totalElements = result.data.totalElements;
          vm.resultadoPesquisa = result.data.content;
        } else {
          MensagensService.exibirMensagemErro('Nenhum imóvel encontrado');
        }
      });
    }

    vm.validarFormulario = function () {
      if ($scope.consultarHomologarImovelForm.$valid) {
        pesquisar();
      } else {
        $scope.consultarHomologarImovelForm.$setSubmitted();
        MensagensService.exibirMensagemCamposObrigatorios();
      }
    };

    vm.limpar = function () {
      vm.imovelPesquisa = {};
      vm.imovel.situacaoCadastro = null;
      $scope.consultarHomologarImovelForm.$setPristine();
      $scope.consultarHomologarImovelForm.$setUntouched();
    };

    vm.page = function () {
      var imovelSerializado = vm.imovelPesquisa;
      return HomologacaoImovelService.buscarTodos(
        imovelSerializado,
        vm.paginacao.page,
        vm.paginacao.limit
      ).then(function (result) {
        vm.resultadoPesquisa = result.data.content;
      });
    };

    vm.showLocalizacaoImovelDialog = function (ev, item) {
      $mdDialog.show({
        controller: localizacaoImovelHomologarCtrl,
        templateUrl:
          'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-homologar/dialog-detalhar-homologar-imoveis.html',
        parent: angular.element($document[0].body),
        targetEvent: ev,
        clickOutsideToClose: true,
        onComplete: notificaDiretivaMapa,
        locals: {
          imovel: item,
        },
      });
    };

    vm.verLocalizacao = function (ev, item) {
      $mdDialog.show({
        controller: localizacaoImovelHomologarCtrl,
        templateUrl:
          'scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-localizacao-imovel.html',
        parent: angular.element($document[0].body),
        targetEvent: ev,
        clickOutsideToClose: true,
        locals: {
          imovel: item,
        },
      });
    };

    var notificaDiretivaMapa = function () {
      NotificadorService.notify(
        $scope,
        vm.resultadoPesquisa,
        notifiers.RESULTADOPESQUISARIMOVEL
      );
    };

    var localizacaoImovelHomologarCtrl = function (
      $scope,
      $http,
      $mdDialog,
      imovel
    ) {
      $scope.imovel = imovel;

      $scope.fechar = function () {
        $mdDialog.cancel();
      };
    };
    localizacaoImovelHomologarCtrl.$inject = ["$scope", "$http", "$mdDialog", "imovel"];

    vm.irParaHomologacao = function (id) {
      $state.go('INCORPORACAO.homologarImovelDetalhar', { id: id });
    };

    vm.pesquisaMunicipio = function (uf) {
      // console.log('alterou a UF');
      if (uf && uf.sigla) {
        vm.progress();
        vm.municipios = [];
        // console.log('carrega municípios');
        MunicipioService.TodosMunicipios.query({
          uf: uf.sigla,
        }).$promise.then(function (result) {
          vm.municipios = result;
          vm.closeProgress();
        });
        // console.log('ajusta UF');
        vm.imovelPesquisa.uf = uf.sigla;
      }
    };

    vm.apresentaBotao = function () {
      if (vm.cepPreenchido === true) {
        return vm.isCepGenerico && !vm.cepValido
      } else {
        return true;
      }
    }
    vm.formataCpfCnpj = function (documento) {
      return CpfCnpjService.formataCpfCnpj(documento);
    };

    $scope.$watch('consultarHomologarCtrl.imovelPesquisa.exibirImoveisCadastradosEditadosDoUsuario', function (
      value
    ) {
      vm.cpfUsuario = null;
      if (value && $scope.usuarioLogado && $scope.usuarioLogado.cpf) {
        vm.imovelPesquisa.cpfUsuario = $scope.usuarioLogado.cpf;
      }
    });

    vm.selecionaMunicipio = function () {
      // console.log('alterou município');
      if (vm.imovelPesquisa.municipio && vm.imovelPesquisa.municipio.nome) {
        // console.log('ajustou município');
        vm.imovelPesquisa.municipio = vm.imovelPesquisa.municipio.nome;
      }
    };

    vm.buscaCidadesPorPais = function () {
      vm.imovelPesquisa.cidadeExterior = null;
      if ( vm.imovelPesquisa.pais !=null){
        var pais = vm.imovelPesquisa.pais;
        if (pais != 1 && pais != null) {
            CidadesExtService.query({id_pais: pais}).$promise.then(function (result) {
                vm.cidades = result;
            });
        }
      }
   };

    init();
  }
})();

(function () {
'use strict';
angular.module('su-cadastro-imovel')

	.factory('MensagemService', ["$resource", function ($resource) {

	    var mensagens = {};
	    mensagens.listarMensagens = function(){
	    	return [
						{id : 1, data : new Date(), assunto : "Eu sou um teste de mensagem do SPU 1", isLida : 0},
						{id : 2, data : new Date(), assunto : "Eu sou um teste de mensagem do SPU 2", isLida : 0},
						{id : 3, data : new Date(), assunto : "Eu sou um teste de mensagem do SPU 3", isLida : 0},
						{id : 4, data : new Date(), assunto : "Eu sou um teste de mensagem do SPU 4", isLida : 0},
						{id : 5, data : new Date(), assunto : "Eu sou um teste de mensagem do SPU 5", isLida : 0},
						{id : 6, data : new Date(), assunto : "Eu sou um teste de mensagem do SPU 6", isLida : 0},
						{id : 7, data : new Date(), assunto : "Eu sou um teste de mensagem do SPU 6", isLida : 0},
						{id : 8, data : new Date(), assunto : "Eu sou um teste de mensagem do SPU 6", isLida : 0},
						{id : 9, data : new Date(), assunto : "Eu sou um teste de mensagem do SPU 6", isLida : 0}
	    	        ]
	    }

	    return mensagens;
	}])
	.factory('InformesService', ["$resource", function ($resource) {

	    var informes = {};
	    informes.listarInformes = function(){
	    	return [
						{id : 1, titulo : "Titulo 1", data : new Date(), descricao : "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras eu pharetra elit. Sed semper est hendrerit metus rutrum pellentesque. Duis tempus non odio sit amet dapibus. Morbi in augue ut augue condimentum finibus. Donec suscipit, lacus ut scelerisque sagittis, ante nisl congue est, eu cursus nisi arcu sed sem. Phasellus eu diam mattis velit suscipit scelerisque at nec nibh. Nam sed dolor ac purus convallis fringilla a nec nisl. Nunc elit eros, fringilla eget consectetur at, venenatis nec arcu. Fusce rutrum libero enim, a vestibulum eros bibendum vel. Integer magna tortor, maximus interdum eleifend sed, scelerisque sed nunc."},
						{id : 2, titulo : "Titulo 2", data : new Date(), descricao : "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras eu pharetra elit. Sed semper est hendrerit metus rutrum pellentesque. Duis tempus non odio sit amet dapibus. Morbi in augue ut augue condimentum finibus. Donec suscipit, lacus ut scelerisque sagittis, ante nisl congue est, eu cursus nisi arcu sed sem. Phasellus eu diam mattis velit suscipit scelerisque at nec nibh. Nam sed dolor ac purus convallis fringilla a nec nisl. Nunc elit eros, fringilla eget consectetur at, venenatis nec arcu. Fusce rutrum libero enim, a vestibulum eros bibendum vel. Integer magna tortor, maximus interdum eleifend sed, scelerisque sed nunc."},
						{id : 3, titulo : "Titulo 3", data : new Date(), descricao : "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras eu pharetra elit. Sed semper est hendrerit metus rutrum pellentesque. Duis tempus non odio sit amet dapibus. Morbi in augue ut augue condimentum finibus. Donec suscipit, lacus ut scelerisque sagittis, ante nisl congue est, eu cursus nisi arcu sed sem. Phasellus eu diam mattis velit suscipit scelerisque at nec nibh. Nam sed dolor ac purus convallis fringilla a nec nisl. Nunc elit eros, fringilla eget consectetur at, venenatis nec arcu. Fusce rutrum libero enim, a vestibulum eros bibendum vel. Integer magna tortor, maximus interdum eleifend sed, scelerisque sed nunc."},
						{id : 4, titulo : "Titulo 4", data : new Date(), descricao : "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras eu pharetra elit. Sed semper est hendrerit metus rutrum pellentesque. Duis tempus non odio sit amet dapibus. Morbi in augue ut augue condimentum finibus. Donec suscipit, lacus ut scelerisque sagittis, ante nisl congue est, eu cursus nisi arcu sed sem. Phasellus eu diam mattis velit suscipit scelerisque at nec nibh. Nam sed dolor ac purus convallis fringilla a nec nisl. Nunc elit eros, fringilla eget consectetur at, venenatis nec arcu. Fusce rutrum libero enim, a vestibulum eros bibendum vel. Integer magna tortor, maximus interdum eleifend sed, scelerisque sed nunc."},
						{id : 5, titulo : "Titulo 5", data : new Date(), descricao : "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras eu pharetra elit. Sed semper est hendrerit metus rutrum pellentesque. Duis tempus non odio sit amet dapibus. Morbi in augue ut augue condimentum finibus. Donec suscipit, lacus ut scelerisque sagittis, ante nisl congue est, eu cursus nisi arcu sed sem. Phasellus eu diam mattis velit suscipit scelerisque at nec nibh. Nam sed dolor ac purus convallis fringilla a nec nisl. Nunc elit eros, fringilla eget consectetur at, venenatis nec arcu. Fusce rutrum libero enim, a vestibulum eros bibendum vel. Integer magna tortor, maximus interdum eleifend sed, scelerisque sed nunc."},
						{id : 6, titulo : "Titulo 6", data : new Date(), descricao : "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras eu pharetra elit. Sed semper est hendrerit metus rutrum pellentesque. Duis tempus non odio sit amet dapibus. Morbi in augue ut augue condimentum finibus. Donec suscipit, lacus ut scelerisque sagittis, ante nisl congue est, eu cursus nisi arcu sed sem. Phasellus eu diam mattis velit suscipit scelerisque at nec nibh. Nam sed dolor ac purus convallis fringilla a nec nisl. Nunc elit eros, fringilla eget consectetur at, venenatis nec arcu. Fusce rutrum libero enim, a vestibulum eros bibendum vel. Integer magna tortor, maximus interdum eleifend sed, scelerisque sed nunc."},
						{id : 7, titulo : "Titulo 7", data : new Date(), descricao : "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras eu pharetra elit. Sed semper est hendrerit metus rutrum pellentesque. Duis tempus non odio sit amet dapibus. Morbi in augue ut augue condimentum finibus. Donec suscipit, lacus ut scelerisque sagittis, ante nisl congue est, eu cursus nisi arcu sed sem. Phasellus eu diam mattis velit suscipit scelerisque at nec nibh. Nam sed dolor ac purus convallis fringilla a nec nisl. Nunc elit eros, fringilla eget consectetur at, venenatis nec arcu. Fusce rutrum libero enim, a vestibulum eros bibendum vel. Integer magna tortor, maximus interdum eleifend sed, scelerisque sed nunc."},
						{id : 8, titulo : "Titulo 8", data : new Date(), descricao : "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras eu pharetra elit. Sed semper est hendrerit metus rutrum pellentesque. Duis tempus non odio sit amet dapibus. Morbi in augue ut augue condimentum finibus. Donec suscipit, lacus ut scelerisque sagittis, ante nisl congue est, eu cursus nisi arcu sed sem. Phasellus eu diam mattis velit suscipit scelerisque at nec nibh. Nam sed dolor ac purus convallis fringilla a nec nisl. Nunc elit eros, fringilla eget consectetur at, venenatis nec arcu. Fusce rutrum libero enim, a vestibulum eros bibendum vel. Integer magna tortor, maximus interdum eleifend sed, scelerisque sed nunc."},
						{id : 9, titulo : "Titulo 9", data : new Date(), descricao : "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras eu pharetra elit. Sed semper est hendrerit metus rutrum pellentesque. Duis tempus non odio sit amet dapibus. Morbi in augue ut augue condimentum finibus. Donec suscipit, lacus ut scelerisque sagittis, ante nisl congue est, eu cursus nisi arcu sed sem. Phasellus eu diam mattis velit suscipit scelerisque at nec nibh. Nam sed dolor ac purus convallis fringilla a nec nisl. Nunc elit eros, fringilla eget consectetur at, venenatis nec arcu. Fusce rutrum libero enim, a vestibulum eros bibendum vel. Integer magna tortor, maximus interdum eleifend sed, scelerisque sed nunc."},
						{id : 10, titulo : "Titulo 10", data : new Date(), descricao : "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras eu pharetra elit. Sed semper est hendrerit metus rutrum pellentesque. Duis tempus non odio sit amet dapibus. Morbi in augue ut augue condimentum finibus. Donec suscipit, lacus ut scelerisque sagittis, ante nisl congue est, eu cursus nisi arcu sed sem. Phasellus eu diam mattis velit suscipit scelerisque at nec nibh. Nam sed dolor ac purus convallis fringilla a nec nisl. Nunc elit eros, fringilla eget consectetur at, venenatis nec arcu. Fusce rutrum libero enim, a vestibulum eros bibendum vel. Integer magna tortor, maximus interdum eleifend sed, scelerisque sed nunc."},
					]
	    }

	    return informes;
	}]);
})();


(function () {
'use strict';
angular.module('su-cadastro-imovel')
    .controller('HomeController', ["$scope", "$mdDialog", "MensagemService", "InformesService", "comumService", function ($scope, $mdDialog, MensagemService, InformesService, comumService) {

        var self = this;
        self.teste = {};

        /*MENSAGENS*/

        self.mensagens = MensagemService.listarMensagens();
        self.contadorMensagens = 0;
      init();
      function init() {
        comumService.buscarUrlsMenusPorAmbiente().then(function (response) {
          location.href = response.data.integrador;
        });
      };

        var contarMensagensNaoLidas = function(listaMensagens){
        	for(var i = 0; i < listaMensagens.length; i++){
        		if(listaMensagens[i].isLida === 0){
        			self.contadorMensagens++;
        		}
        	}
        }

        contarMensagensNaoLidas(self.mensagens);

        self.mensagemLida = function(indexMensagem){
        	if(self.mensagens[indexMensagem].isLida === 0){
        		self.mensagens[indexMensagem].isLida = 1;
        		self.contadorMensagens--;
        	}
        }

        self.showMensagesDialog = function(ev) {
            $mdDialog.show({
                controller: mesagemDialogController,
                templateUrl: 'scripts-cadastro-imovel/pages/home/view/componentes/mensagem.dialog.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose:true,
                locals: {
                    mensagens: self.mensagens
                }
            });
        };

      var mesagemDialogController = function($scope, $mdDialog, mensagens) {
        $scope.mensagens = mensagens;

        $scope.fechar = function () {
          $mdDialog.cancel();
        };
      };
      mesagemDialogController.$inject = ["$scope", "$mdDialog", "mensagens"];


      /*FUNCOES FAVORITAS*/

        self.redirecionaFuncaoFavorita = function(){

        }

        /*INFORMES*/

        self.informes = InformesService.listarInformes();
        self.informeSelecionado = {};

        self.resetInformeSelecionado = function(){
        	self.informeSelecionado = {};
        }

        self.continuarLendo = function(index){
        	self.informeSelecionado = self.informes[index];
        }

        self.showInformesDialog = function(ev) {
            $mdDialog.show({
                controller: informeDialogController,
                templateUrl: 'scripts-cadastro-imovel/pages/home/view/componentes/informe.dialog.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose:true,
                locals: {
                    informes: self.informes,
                    informeSelecionado : self.informeSelecionado
                }
            });
        };

      var informeDialogController = function($scope, $mdDialog, informes, informeSelecionado) {
        $scope.informes = informes;
        $scope.informeSelecionado = informeSelecionado;

        $scope.fechar = function () {
          $mdDialog.cancel();
        };

        $scope.descricaoExtensa = function(descricaoInforme){
          var descricao;
          if(descricaoInforme.length > 200){
            descricao = descricaoInforme.substring(0, 201);
            descricao += "...";
            return descricao;
          }else{
            return descricaoInforme;
          }
        }

        $scope.controleExibicao = true;
        $scope.exibirDescricao = function(index){
          $scope.informeSelecionado = $scope.informes[index];
          $scope.controleExibicao = false;
        }

        if(informeSelecionado.id !== undefined){
          $scope.controleExibicao = false;
        }

        $scope.voltar = function(){
          $scope.controleExibicao = true;
        }

      };
      informeDialogController.$inject = ["$scope", "$mdDialog", "informes", "informeSelecionado"];



		self.descricaoExtensa = function(descricaoInforme){
			var descricao;
			if(descricaoInforme.length > 200){
				descricao = descricaoInforme.substring(0, 201);
				descricao += "...";
				return descricao;
			}else{
				return descricaoInforme;
			}
		}

    }]);
})();

(function () {
    'use strict';
    angular.module('su-cadastro-imovel')
        .factory("ValidaBenfeitoriaService",["$resource", "urlBase", function($resource, urlBase) {
            return $resource(urlBase + '/imovel/validaIntersecoesBenfeitorias');
        }])
        .factory("DominioBenfeitoria", ["$resource", "urlBase", function($resource, urlBase){

            return {
                tiposBenfeitoria : $resource(urlBase + 'dominio/tipo-benfeitoria/all',null,{cache : true}),
                tipoEspecializacaoComplementar : $resource(urlBase + 'dominio/tipo-especializacao-complementar/all',null,{cache : true}),
                tipoEspecializacaoEdificacao : $resource(urlBase + 'dominio/tipo-especializacao-edificacao/all',null,{cache : true}),
                materialConstrucao : $resource(urlBase + 'dominio/tipo-material-construcao/all',null,{cache : true}),
                estadoConservacao : $resource(urlBase + 'dominio/estado-conservacao/all',null,{cache : true}),
                tipoEstrutura : $resource(urlBase + 'dominio/tipo-estrutura/all',null,{cache : true}),
                condicaoAcessibilidade : $resource(urlBase + '/dominio/padrao-acessibilidade/all',null,{cache : true}),
                padraoAcabamento : $resource(urlBase + '/dominio/tipo-acabamento/all',null,{cache : true}),
                tipoEnce : $resource(urlBase + '/dominio/tipo-ence/all',null,{cache : true}),
                tipoEnceParcial : $resource(urlBase + '/dominio/tipo-ence-parcial/all',null,{cache : true}),
                nivelEnce : $resource(urlBase + '/dominio/nivel-ence/all',null,{cache : true})
            }
        }])
        .factory("DocumentoBenfeitoria", ["$resource", "urlBase", function($resource, urlBase){

            return $resource(urlBase + "/imovel/benfeitoria/converter-arquivo", null, {
                save : {
                    method : "POST",
                    transformRequest : function(data){
                        return angular.toJson(data);
                    },
                    headers: {'Content-Type':undefined, enctype:'multipart/form-data'}
                }
            });

        }])
        .factory('UploadImagemBenfeitoria', ["$resource", "urlBase", function($resource, urlBase){

            function formDataObject (data) {
                var fd = new FormData();
                angular.forEach(data, function(value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + '/imovel/benfeitoria/upload', {foto: "@foto"}, {
                'save' : {
                    method : 'POST',
                    transformRequest : formDataObject,
                    headers: {'Content-Type':undefined, enctype:'multipart/form-data'},
                    params: {
                        foto: "@foto"
                    }
                }
            });
        }])
        .factory('UploadPlantaBenfeitoria', ["$resource", "urlBase", function($resource, urlBase){

            function formDataObject (data) {
                var fd = new FormData();
                angular.forEach(data, function(value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + '/imovel/planta-benfeitoria/upload', {planta: "@planta"}, {
                upload : {
                    method : 'POST',
                    transformRequest : formDataObject,
                    headers: {'Content-Type':undefined, enctype:'multipart/form-data'},
                    params: {
                        planta: "@planta"
                    }
                }
            });
        }])
        .factory("ExibirPlantaBenfeitoria", ["$http", "urlBase", function($http, urlBase){

            function obterPlantaBenfeitoria(id) {
                return $http.get(urlBase + 'imovel/planta-benfeitoria/' + id);
            }


            function setMime(string){
                if(string.indexOf("PDF") !== -1){
                    return "application/pdf";
                }else if(string.indexOf("PNG") !== -1){
                    return "image/png";
                }else{
                    return "image/jpg";
                }
            }

            return {
                visualizarPlanta: function(id,$nomeFile){
                    return $http({
                        url: 'http://' + window.location.host + urlBase + '/imovel/planta-benfeitoria/listar',
                        method: 'GET',
                        responseType: 'arraybuffer',
                        params: {id : id}
                    }).success(function (data){
                        var file = new Blob([data], {type: setMime($nomeFile)});
                        var fileURL = URL.createObjectURL(file);
                        window.open(fileURL);
                    })
                },
                obterPlantaBenfeitoria: obterPlantaBenfeitoria
            }
        }])
        .factory("ValidaTerrenoBenfeitoriaService", ["$resource", "urlBase", function($resource, urlBase) {
            return  $resource(urlBase + 'imovel/benfeitoria/validar', {dados: "@dados"},
                {
                    validar:{
                        method:"POST",
                        params: {
                            dados: "@dados"
                        }
                    }
                });
        }])
        .factory("UtilizacoesService", ["$resource", "urlBase", function ($resource, urlBase) {
          return $resource(urlBase + 'imovel/utilizacoes-spiu/listar/:idImovel', {idImovel : "@idImovel"});
        }])

        .factory("MigracaoBenfeitoria", ["$http", "urlBase", function ($http, urlBase) {

          return { liberada: function() {
                              return $http(
                              {  url: 'https://' + window.location.host + urlBase + 'imovel/liberada-migracao-benfeitoria',
                                 method: 'GET',
                                 responseType: 'text'
                              })
                              .then(function (response){
                                return response.data;
                              });
                            }
                }
        }]);
})();

(function(){
  angular.module('su-cadastro-imovel').factory('BenfeitoriaNotificadorService', ["$rootScope", function($rootScope) {


    return {
        subscribe: function(scope, callback) {
            var handler = $rootScope.$on('notificarService', callback);
            scope.$on('$destroy', handler);
        },

        notify: function(scope,object) {
            $rootScope.$emit('notificarService',scope,object);
        }

    };
}]);


})();

(function(){
  "use strict";
  angular.module("su-cadastro-imovel").controller("BenfeitoriaController", ['$scope', '$mdDialog', 'DominioBenfeitoria',
    'DocumentoBenfeitoria', 'ValidaPontoImovelService', 'UploadImagemBenfeitoria', 'UploadPlantaBenfeitoria',
    'fileService', 'ExibirPlantaBenfeitoria', 'verificaObrigatorios', 'ArquivoBenfeitoriaShapeFileService',
    'BenfeitoriaNotificadorService', 'NotificadorService', 'NOTIFICADORES', 'MAPA_CONSTANTS_INCORPORACAO', 'ValidaTerrenoBenfeitoriaService', 'MensagensService',

    function BenfeitoriaController($scope, $mdDialog, DominioBenfeitoria, DocumentoBenfeitoria,
                                   ValidaPontoImovelService, UploadImagemBenfeitoria,UploadPlantaBenfeitoria,fileService,
                                   ExibirPlantaBenfeitoria,verificaObrigatorios, ArquivoBenfeitoriaShapeFileService,
                                   BenfeitoriaNotificadorService, NotificadorService, NOTIFICADORES, MAPA_CONSTANTS_INCORPORACAO,
                                   ValidaTerrenoBenfeitoriaService, MensagensService){

      var vm = this,
          notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES;
      vm.benfeitoria = $scope.$parent.benfeitoria;
      vm.showMapaCadastroBenfeitoria = false;
      vm.benfeitoriaClone = angular.copy($scope.$parent.benfeitoria);
      vm.benfeitoria.imagens = $scope.$parent.imagensBenfeitoria;
      vm.benfeitorias = $scope.$parent.benfeitorias;
      vm.dominios = DominioBenfeitoria;
      vm.slickConfig1Loaded = true;
      vm.planta={};
      vm.benfeitoria.edificacao = vm.benfeitoria.edificacao ? vm.benfeitoria.edificacao : {};
      vm.benfeitoria.edificacao.areaProjecao = vm.benfeitoria.edificacao.areaProjecao ? vm.benfeitoria.edificacao.areaProjecao : 0.00;
      $scope.geometrias = $scope.$parent.geometrias;
      $scope.imovel = $scope.$parent.imovel;
      vm.matriculaIsPreenchida=$scope.imovel.incorporacao.numeroMatricula == undefined || $scope.imovel.incorporacao.numeroMatricula == "" ? false: true;
      $scope.addFoto = false;
      $scope.addPlanta = false;
      $scope.imagem = {};
      $scope.hoje = new Date();
      $scope.imagensAlbum = [];
      $scope.tmp = {};
      $scope.isEdificacao = undefined;
      vm.benfeitoria.tipoArquivo = 2;
      vm.validarBenfeitoria = false;
      vm.benfeitoria.file = undefined;
      vm.includeMap = false;

      $scope.forms = {};
      $scope.tipoBenfeitoria = vm.dominios.tiposBenfeitoria.query();
      $scope.tipoEspecializacaoComplementar = vm.dominios.tipoEspecializacaoComplementar.query();
      $scope.tipoEspecializacaoEdificacao = vm.dominios.tipoEspecializacaoEdificacao.query();
      $scope.tipoEnce = vm.dominios.tipoEnce.query();
      $scope.tipoEnceParcial = vm.dominios.tipoEnceParcial.query();
      $scope.nivelEnce = vm.dominios.nivelEnce.query();
      $scope.materialConstrucao = vm.dominios.materialConstrucao.query();
      $scope.estadoConservacao = vm.dominios.estadoConservacao.query();
      $scope.tipoEstrutura = vm.dominios.tipoEstrutura.query();
      $scope.condicaoAcessibilidade = vm.dominios.condicaoAcessibilidade.query();
      $scope.padraoAcabamento = vm.dominios.padraoAcabamento.query();
      $scope.isEditando = false;
      $scope.alterados={
        alterado:'dado-alterado',
        original:'dado-nao-alterado',
        loadingTemplate:true
      };

      NotificadorService.subscribe($scope, function(evento){
        vm.includeMap = true;
      }, NOTIFICADORES.INJETARMAPADIALOGADICIONARBENFEITORIA);

      vm.secaoAnterior = '';
      vm.validaDadosLocalizacaoImovel = false;
      vm.ultimaSecaoValidada = '';
      vm.secoesExpandidas = [];
      vm.validaSecao = function(ev, form, tab, secaoAtual){
        var isSecaoAnteriorExpandida = false;
        var isSecaoAtualExpandida = false;
        var secaoAtualIndex = -1;
        var errorsForms = [];

        if(vm.secaoAnterior === ''){
          vm.secaoAnterior = '.secaoDadosAcessaoBenfeitoria';
          vm.secoesExpandidas.push('.secaoDadosAcessaoBenfeitoria');
        }

        angular.forEach(vm.secoesExpandidas, function(value, key){
          if(vm.secaoAnterior === value){
            isSecaoAnteriorExpandida = true;
          }

          if(secaoAtual === value){
            isSecaoAtualExpandida = true;
            secaoAtualIndex = key;
          }
        });

        if(isSecaoAtualExpandida){
          vm.secoesExpandidas.splice(secaoAtualIndex, 1);
        }else{
          vm.secoesExpandidas.push(secaoAtual);
        }

        if((isSecaoAnteriorExpandida && vm.secaoAnterior != vm.ultimaSecaoValidada) || tab === -1){
          popularErrorsFormsValidacaoSecao(vm.secaoAnterior, form, errorsForms);
          vm.ultimaSecaoValidada = vm.secaoAnterior;
        }else if(isSecaoAtualExpandida && secaoAtual !== vm.ultimaSecaoValidada){
          popularErrorsFormsValidacaoSecao(secaoAtual, form, errorsForms);
          vm.ultimaSecaoValidada = secaoAtual;
        }else{
          vm.secaoAnterior = secaoAtual;
          return;
        }

        vm.secaoAnterior = secaoAtual;

        if(errorsForms.length > 0){
          showObrigatoriosSecaoModalValidacao(errorsForms, ev, tab, form);
        }
      };

      function popularErrorsFormsValidacaoSecao(secao, form, errorsForms){
        var elementos = [];
        angular.forEach(angular.element(secao), function(value, key){
          elementos.push(value.getAttribute("name"));
        });

        angular.forEach(form.$error.required, function(value, key){
          angular.forEach(elementos, function(valueElementos, keyElementos){
            if(valueElementos === value.$name){
              errorsForms.push(valueElementos);
            }
          });
        });

        return errorsForms;
      }

      function showObrigatoriosSecaoModalValidacao(errorsForms, ev, tab, form){
        var params = {};
        params.errorsForms = errorsForms;
        params.ev = ev;
        params.tab = tab;
        params.form = form;

        NotificadorService.notify($scope, params, NOTIFICADORES.SHOWMODALVALIDACAOBENFEITORIA);

      }

      if(vm.benfeitoria.codigo !== undefined){
        $scope.isEditando = true;
        $scope.benfeitoria = vm.benfeitoria;
        if(vm.benfeitoria.codigo.indexOf("E") != -1)
          $scope.isEdificacao = 1;
        else
          $scope.isEdificacao = 2;
      }else{
        $scope.isEditando = false;
      }

      if($scope.isEditando==false){
        vm.benfeitoria.plantas=[];
      }

      $scope.$on("removerGeometriaBenfeiotira", function(event, data){
        if(data){
          vm.benfeitoria.geometriaBenfeitoria = undefined;
        }
      });

      $scope.$on("geometriaAlterada", function(event, data){
        if(data.codigo.indexOf("E") != -1){
          vm.benfeitoria.edificacao.geometriaEdificacao = data.geo;
        }else{
          vm.benfeitoria.benfeitoriaComplementar.geometriaBenfeitoriaComplementar = data.geo;
        }
        vm.benfeitoria.geometriaBenfeitoria = data.geo;
      });

      $scope.limpar = function(){
        vm.benfeitoria = {};
        $scope.forms.adicionarBenfeitoriaForm.$setPristine();
        $scope.forms.adicionarBenfeitoriaForm.$setUntouched();
      }


      var incluir = function(){
        var data = vm.benfeitoria;
        $scope.$emit("benfeitoriaIncluida", data);
        $mdDialog.cancel();
      };


      $scope.validarCampos = function(){
        if(vm.benfeitoria.plantas && vm.benfeitoria.plantas.length > 0 && !vm.validarCampos){
          $scope.forms.plantaBenfeitoria.$setPristine();
          if($scope.forms.plantaBenfeitoria.$error.required!=undefined &&
            $scope.forms.plantaBenfeitoria.$error.required.length==1 &&
            $scope.forms.plantaBenfeitoria.$error.required[0].$name=="forms.plantaBenfeitoria"){
            $scope.forms.adicionarBenfeitoriaForm.$invalid=false;
          }
        }
        if($scope.forms.adicionarBenfeitoriaForm.$invalid){
          $scope.errorsForms=[];
          $scope.submittedBenfeitoria = true;
          $scope.errorsForms=verificaObrigatorios.getValoresByForm($scope.forms.adicionarBenfeitoriaForm,'imovel');

        }else{

           if ($scope.isEditando) {

               if (vm.benfeitoria.codigo.startsWith("C", 0)) {
                    if (!vm.benfeitoria.benfeitoriaComplementar.geometriaBenfeitoriaComplementar){
                        MensagensService.exibirMensagemErro("É necessário informar uma geometria para a benfeitoria");
                        return;
                    }
               } else if (!vm.benfeitoria.edificacao.geometriaEdificacao) {

                   MensagensService.exibirMensagemErro("É necessário informar uma geometria para a benfeitoria");
                   return;
               }

           } else {
               if (!(vm.benfeitoria.geometriaBenfeitoria)) {
                   MensagensService.exibirMensagemErro("É necessário informar uma geometria para a benfeitoria");
                   return;
               }
           }

          incluir();
          $scope.submittedBenfeitoria = false;
        }
      };


      NotificadorService.subscribe($scope, function (evento, escopo, data) {
         vm.benfeitoria.geometriaBenfeitoria = data.geoBenfeitoria;
         vm.benfeitoria.geometriaBenfeitoriaIsValid = data.isValid;
      }, notifiers.GEOMETRIA_BENFEITORIA_ALTERADA);

        $scope.$on("benfeitoriaDesenhada", function(evt, data){
        if(data.codigo != undefined){
          if(data.codigo.indexOf("E") != -1){
            vm.benfeitoria.edificacao.geometriaEdificacao = data.geo;
          }else{
            vm.benfeitoria.benfeitoriaComplementar.geometriaBenfeitoriaComplementar = data.geo;
          }
        }
        vm.benfeitoria.geometriaBenfeitoria = data.geo;
      });


      $scope.$on('isBenfeitoriaDentroTerreno',function(evt,data){
        vm.validarBenfeitoria = data;
      });

      $scope.file2blob = function(tipo, obj){
        if(validarExtensaoArquivoDocumentacaoBenfeitoria(obj)){
          var r = new FileReader();
          r.onload = function(){
            switch(tipo){
              case "planoProtecaoIncendioFile" : vm.benfeitoria.edificacao.planoProtecaoIncendioStr = r.result; break;
              case "habiteseFile" : vm.benfeitoria.edificacao.habiteseStr = r.result; break;
              case "alvaraConstrucaoFile" : vm.benfeitoria.edificacao.alvaraConstrucaoStr = r.result; break;
              case "laudoInspencaoFile" : vm.benfeitoria.edificacao.laudoInspencaoStr = r.result; break;
              case "outroFile" : vm.benfeitoria.edificacao.outroStr = r.result; break;
            }
          }
          r.readAsBinaryString(obj);
        }else {
          resetUploadsInvalidos(tipo);
        }
      }

      var resetUploadsInvalidos = function(tipo){
        switch(tipo){
          case "planoProtecaoIncendioFile" : $scope.benfeitoria.edificacao.planoProtecaoIncendio = undefined; break;
          case "habiteseFile" : $scope.benfeitoria.edificacao.habitese = undefined; break;
          case "alvaraConstrucaoFile" : $scope.benfeitoria.edificacao.alvaraConstrucao = undefined; break;
          case "laudoPredialFile" : $scope.benfeitoria.edificacao.laudoInspencao = undefined; break;
          case "outroFile" : $scope.benfeitoria.edificacao.outro = undefined; break;
        }
      }

      var validarExtensaoArquivoDocumentacaoBenfeitoria = function(arquivo) {
        if (getExtensaoDocumentacaoBenfeitoria(arquivo.name) == ".pdf")
          return true;
        else if (getExtensaoDocumentacaoBenfeitoria(arquivo.name) == ".png")
          return true;
        else if (getExtensaoDocumentacaoBenfeitoria(arquivo.name) == ".jpg" || getExtensaoDocumentacaoBenfeitoria(arquivo.name) == ".jpeg")
          return true;
        else {
            MensagensService.exibirMensagemErro("Formato de arquivo inválido. São permitidos apenas arquivos do tipo .PDF .PNG .JPEG");
          return false;
        }
      }

      var getExtensaoDocumentacaoBenfeitoria = function(nomeArquivo){
        if(nomeArquivo.endsWith(".jpeg")){
          return nomeArquivo.substring(nomeArquivo.length - 5);
        }
        return nomeArquivo.substring(nomeArquivo.length - 4);
      }

      $scope.habilitarDesenho =function(data){
        $scope.$broadcast('habilitarDesenho',data);
      }

      $scope.uploadArquivoBenfeitoria = function(){
        var file = $('#fileBenfeitoria').get(0).files[0];

        ArquivoBenfeitoriaShapeFileService.upload(file).then(function(result){
          $scope.coordenadasBenfeitoriaShapefile =  result.geometry.coordinates;
          BenfeitoriaNotificadorService.notify($scope,result.geometry.coordinates);
        },function(){
            MensagensService.exibirMensagemErro("É permitido o upload de apenas uma geometria de acessão/benfeitoria por vez.");
        });

      }

      $scope.habilitarUpload = function(){
        $scope.addFoto = true;
        $scope.forms.imagemBenfeitoriaForm.$setUntouched();
        $scope.forms.imagemBenfeitoriaForm.$setPristine();
      };

      $scope.cancelarUpload = function(){
        $scope.addPlanta = false;
      };


      $scope.isCamposUploadImagemValidos = function(){
        $scope.errorsForms = verificaObrigatorios.getValoresByForm($scope.forms.imagemBenfeitoriaForm, 'imovel');
        if($scope.forms.imagemBenfeitoriaForm.foto.$error && $scope.forms.imagemBenfeitoriaForm.foto.$error.maxSize){
          $scope.errorsForms.push('O arquivo da foto não pode ser superior a 5MB.')
        }

        return $scope.errorsForms.length === 0;
      }
      $scope.cancelar = function () {
        $scope.addFoto = false;
      }
      $scope.upload = function(){
        $scope.submittedFoto = true;
        if(validarExtensaoArquivo($scope.imagem.foto) && $scope.isCamposUploadImagemValidos()){
          UploadImagemBenfeitoria.save($scope.imagem).$promise.then(function(data){
            vm.benfeitoria.imagens.push(data);
            gerarUrlBinario(data.imagem);
            $scope.imagem = {};
            confirmarUpload();
            $('#slicker').slick('unslick');
            $('#slicker').slick();
          });
        }else{
          $mdDialog.show({
            controllerAs: 'vm',
            controller: verificaObrigatorios.getController(),
            preserveScope: true,
            autoWrap: true,
            skipHide: true,
            locals:{
              errorsForms:$scope.errorsForms
            },
            templateUrl: 'scripts-cadastro-imovel/app/views/dialog-validacao.html'
          });
        }
      }

      var validarExtensaoArquivo = function(arquivo) {
        if(!arquivo)
          return false;
        else if (getExtensao(arquivo.name) == ".bmp")
          return true;
        else if (getExtensao(arquivo.name) == ".png")
          return true;
        else if (getExtensao(arquivo.name) == ".jpg" || getExtensao(arquivo.name) == ".jpeg" )
          return true;
        else {
            MensagensService.exibirMensagemErro( "Formato de arquivo inválido. São permitidos apenas arquivos do tipo .BMP, JPG ou PNG.");
          return false;
        }
      }

      var getExtensao = function(nomeArquivo){
        if(nomeArquivo.endsWith(".jpeg")){
          return nomeArquivo.substring(nomeArquivo.length - 5);
        }
        return nomeArquivo.substring(nomeArquivo.length - 4);
      }

      function confirmarUpload(){
        $scope.addFoto = false;
      }

      function gerarUrlBinario(blob){
        var url = 'data:image/jpeg;base64,'+blob;
        $scope.imagensAlbum.push(url);
      }

      $scope.removerImagemBenfeitoria = function(index){
        vm.benfeitoria.imagens.splice(index, 1);
        $scope.imagensAlbum.splice(index, 1);
      }

      $scope.$watch('vm.benfeitoria.edificacao.areaProjecao', function(value){
        if(value) {
          var taxa = value / $scope.imovel.terreno.areaTotalCadastral;
          var precisao = taxa.toString().substring(0,4);
          vm.benfeitoria.edificacao.taxaOcupacao = parseFloat(precisao);
          if($scope.isEditando){
            if(!vm.benfeitoriaClone.edificacao){
              vm.benfeitoriaClone.edificacao={};
              $scope.alterados.areaProjecao=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.areaProjecao,vm.benfeitoriaClone.edificacao.areaProjecao) ?
                $scope.alterados.areaProjecao=$scope.alterados.alterado
                :
                $scope.alterados.areaProjecao=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.areaConstruida', function(value){
        if(value && vm.benfeitoria.edificacao != undefined) {
          var indice = value / $scope.imovel.terreno.areaTotalCadastral;
          var precisao = indice.toString().substring(0,4);
          if(vm.benfeitoria.edificacao!=null){
            vm.benfeitoria.edificacao.indiceAproveitamento = parseFloat(precisao);
          }
          if($scope.isEditando){
            !angular.equals(vm.benfeitoria.areaConstruida,vm.benfeitoriaClone.areaConstruida) ?
              $scope.alterados.areaConstruida=$scope.alterados.alterado
              :
              $scope.alterados.areaConstruida=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.tipoBenfeitoria', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.tipoBenfeitoria){
            vm.benfeitoriaClone.tipoBenfeitoria = angular.copy(vm.benfeitoria.tipoBenfeitoria);
            $scope.alterados.tipoBenfeitoria=$scope.alterados.original;
          }else{
            !angular.equals(vm.benfeitoria.tipoBenfeitoria.id, vm.benfeitoriaClone.tipoBenfeitoria.id) ?
              $scope.alterados.tipoBenfeitoria=$scope.alterados.alterado
              :
              $scope.alterados.tipoBenfeitoria=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.tipoEspecializacaoEdificacao=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.tipoEspecializacaoEdificacao){
              vm.benfeitoriaClone.edificacao.tipoEspecializacaoEdificacao = angular.copy(vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao);
            }
            !angular.equals(vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id, vm.benfeitoriaClone.edificacao.tipoEspecializacaoEdificacao.id) ?
              $scope.alterados.tipoEspecializacaoEdificacao=$scope.alterados.alterado
              :
              $scope.alterados.tipoEspecializacaoEdificacao=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.benfeitoriaComplementar){
            vm.benfeitoriaClone.benfeitoriaComplementar={};
            $scope.alterados.tipoEspecializacaoComplementar=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.benfeitoriaComplementar.tipoEspecializacaoComplementar){
              vm.benfeitoriaClone.benfeitoriaComplementar.tipoEspecializacaoComplementar = angular.copy(vm.benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar);
            }
            !angular.equals(vm.benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar.id, vm.benfeitoriaClone.benfeitoriaComplementar.tipoEspecializacaoComplementar.id) ?
              $scope.alterados.tipoEspecializacaoComplementar=$scope.alterados.alterado
              :
              $scope.alterados.tipoEspecializacaoComplementar=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.nome', function(value){
        if(value && $scope.isEditando){
          !angular.equals(vm.benfeitoria.nome,vm.benfeitoriaClone.nome) ?
            $scope.alterados.nome=$scope.alterados.alterado
            :
            $scope.alterados.nome=$scope.alterados.original;
        }
      });

      $scope.$watch('vm.benfeitoria.benfeitoriaComplementar.materialConstrucao', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.benfeitoriaComplementar){
            vm.benfeitoriaClone.benfeitoriaComplementar={};
            $scope.alterados.materialConstrucao=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.benfeitoriaComplementar.materialConstrucao){
              vm.benfeitoriaClone.benfeitoriaComplementar.materialConstrucao = angular.copy(vm.benfeitoria.benfeitoriaComplementar.materialConstrucao);
            }
            !angular.equals(vm.benfeitoria.benfeitoriaComplementar.materialConstrucao.id, vm.benfeitoriaClone.benfeitoriaComplementar.materialConstrucao.id) ?
              $scope.alterados.materialConstrucao=$scope.alterados.alterado
              :
              $scope.alterados.materialConstrucao=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.numeroPavimentos', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.numeroPavimentos=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.numeroPavimentos,vm.benfeitoriaClone.edificacao.numeroPavimentos) ?
              $scope.alterados.numeroPavimentos=$scope.alterados.alterado
              :
              $scope.alterados.numeroPavimentos=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.numeroUnidadesResidenciais', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.numeroUnidadesResidenciais=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.numeroUnidadesResidenciais,vm.benfeitoriaClone.edificacao.numeroUnidadesResidenciais) ?
              $scope.alterados.numeroUnidadesResidenciais=$scope.alterados.alterado
              :
              $scope.alterados.numeroUnidadesResidenciais=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.descricaoAverbacao', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.descricaoAverbacao=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.descricaoAverbacao,vm.benfeitoriaClone.edificacao.descricaoAverbacao) ?
              $scope.alterados.descricaoAverbacao=$scope.alterados.alterado
              :
              $scope.alterados.descricaoAverbacao=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.possuiHabitese', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.possuiHabitese=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.possuiHabitese,vm.benfeitoriaClone.edificacao.possuiHabitese) ?
              $scope.alterados.possuiHabitese=$scope.alterados.alterado
              :
              $scope.alterados.possuiHabitese=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.anoHabitese', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.anoHabitese=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.anoHabitese,vm.benfeitoriaClone.edificacao.anoHabitese) ?
              $scope.alterados.anoHabitese=$scope.alterados.alterado
              :
              $scope.alterados.anoHabitese=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.possuiGaragem', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.possuiGaragem=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.possuiGaragem,vm.benfeitoriaClone.edificacao.possuiGaragem) ?
              $scope.alterados.possuiGaragem=$scope.alterados.alterado
              :
              $scope.alterados.possuiGaragem=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.numeroVagas', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.numeroVagas=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.numeroVagas,vm.benfeitoriaClone.edificacao.numeroVagas) ?
              $scope.alterados.numeroVagas=$scope.alterados.alterado
              :
              $scope.alterados.numeroVagas=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.possuiElevador', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.possuiElevador=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.possuiElevador,vm.benfeitoriaClone.edificacao.possuiElevador) ?
              $scope.alterados.possuiElevador=$scope.alterados.alterado
              :
              $scope.alterados.possuiElevador=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.possuiENCE', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.possuiENCE=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.possuiENCE,vm.benfeitoriaClone.edificacao.possuiENCE) ?
              $scope.alterados.possuiENCE=$scope.alterados.alterado
              :
              $scope.alterados.possuiENCE=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.numeroDormitorios', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.numeroDormitorios=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.numeroDormitorios,vm.benfeitoriaClone.edificacao.numeroDormitorios) ?
              $scope.alterados.numeroDormitorios=$scope.alterados.alterado
              :
              $scope.alterados.numeroDormitorios=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.estadoConservacao', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.estadoConservacao){
            vm.benfeitoriaClone.estadoConservacao = angular.copy(vm.benfeitoria.estadoConservacao);
            $scope.alterados.estadoConservacao=$scope.alterados.original;
          }else{
            !angular.equals(vm.benfeitoria.estadoConservacao.id, vm.benfeitoriaClone.estadoConservacao.id) ?
              $scope.alterados.estadoConservacao=$scope.alterados.alterado
              :
              $scope.alterados.estadoConservacao=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.tipoEstrutura', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.tipoEstrutura=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.tipoEstrutura){
              vm.benfeitoriaClone.edificacao.tipoEstrutura = angular.copy(vm.benfeitoria.edificacao.tipoEstrutura);
              $scope.alterados.tipoEstrutura=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.tipoEstrutura.id, vm.benfeitoriaClone.edificacao.tipoEstrutura.id) ?
                $scope.alterados.tipoEstrutura=$scope.alterados.alterado
                :
                $scope.alterados.tipoEstrutura=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.padraoAcabamento', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.padraoAcabamento=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.padraoAcabamento){
              vm.benfeitoriaClone.edificacao.padraoAcabamento = angular.copy(vm.benfeitoria.edificacao.padraoAcabamento);
              $scope.alterados.padraoAcabamento=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.padraoAcabamento.id, vm.benfeitoriaClone.edificacao.padraoAcabamento.id) ?
                $scope.alterados.padraoAcabamento=$scope.alterados.alterado
                :
                $scope.alterados.padraoAcabamento=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.observacoes', function(value){
        if(value && $scope.isEditando){
          !angular.equals(vm.benfeitoria.observacoes,vm.benfeitoriaClone.observacoes) ?
            $scope.alterados.observacoes=$scope.alterados.alterado
            :
            $scope.alterados.observacoes=$scope.alterados.original;
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.semInformacaoAcessibilidade', function(value){
        if(value && $scope.isEditando){
          !angular.equals(vm.benfeitoria.edificacao.semInformacaoAcessibilidade,vm.benfeitoriaClone.edificacao.semInformacaoAcessibilidade) ?
            $scope.alterados.semInformacaoAcessibilidade=$scope.alterados.alterado
            :
            $scope.alterados.semInformacaoAcessibilidade=$scope.alterados.original;
          if(!vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            //$scope.alterados.vagasSinalizadas=$scope.alterados.original;
            //$scope.alterados.acessoLivreObstaculos=$scope.alterados.original;
            //$scope.alterados.banheiroAcessivel=$scope.alterados.original;
            //$scope.alterados.elevadorAcessivel=$scope.alterados.original;
            $scope.alterados.vagasDefFisicos=$scope.alterados.original;
            $scope.alterados.vagasIdosos=$scope.alterados.original;
            $scope.alterados.calcadasRebaixa=$scope.alterados.original;
            $scope.alterados.calcadasLivres=$scope.alterados.original;
            $scope.alterados.entradaLivre=$scope.alterados.original;
            $scope.alterados.pavimentosLivreCadRodas=$scope.alterados.original;
            $scope.alterados.balcaoInformacoes=$scope.alterados.original;
            $scope.alterados.pisoTatil=$scope.alterados.original;
            $scope.alterados.corredorLargo=$scope.alterados.original;
            $scope.alterados.sanitarioPublico=$scope.alterados.original;
            $scope.alterados.braile=$scope.alterados.original;
            $scope.alterados.libras=$scope.alterados.original;
            $scope.alterados.possuiLaudo=$scope.alterados.original;
            $scope.alterados.linkLaudo=$scope.alterados.original;
            $scope.alterados.possuiPlanoTrabalho=$scope.alterados.original;
            $scope.alterados.linkPlanoTrabalho=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.vagasDefFisicos', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.vagasDefFisicos=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.vagasDefFisicos){
              vm.benfeitoriaClone.edificacao.vagasDefFisicos = angular.copy(vm.benfeitoria.edificacao.vagasDefFisicos);
              $scope.alterados.vagasDefFisicos=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.vagasDefFisicos.id, vm.benfeitoriaClone.edificacao.vagasDefFisicos.id) ?
                $scope.alterados.vagasDefFisicos=$scope.alterados.alterado
                :
                $scope.alterados.vagasDefFisicos=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.vagasIdosos', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.vagasIdosos=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.vagasIdosos){
              vm.benfeitoriaClone.edificacao.vagasIdosos = angular.copy(vm.benfeitoria.edificacao.vagasIdosos);
              $scope.alterados.vagasIdosos=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.vagasIdosos.id, vm.benfeitoriaClone.edificacao.vagasIdosos.id) ?
                $scope.alterados.vagasIdosos=$scope.alterados.alterado
                :
                $scope.alterados.vagasIdosos=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.calcadasRebaixa', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.calcadasRebaixa=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.calcadasRebaixa){
              vm.benfeitoriaClone.edificacao.calcadasRebaixa = angular.copy(vm.benfeitoria.edificacao.calcadasRebaixa);
              $scope.alterados.calcadasRebaixa=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.calcadasRebaixa.id, vm.benfeitoriaClone.edificacao.calcadasRebaixa.id) ?
                $scope.alterados.calcadasRebaixa=$scope.alterados.alterado
                :
                $scope.alterados.calcadasRebaixa=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.calcadasLivres', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.calcadasLivres=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.calcadasLivres){
              vm.benfeitoriaClone.edificacao.calcadasLivres = angular.copy(vm.benfeitoria.edificacao.calcadasLivres);
              $scope.alterados.calcadasLivres=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.calcadasLivres.id, vm.benfeitoriaClone.edificacao.calcadasLivres.id) ?
                $scope.alterados.calcadasLivres=$scope.alterados.alterado
                :
                $scope.alterados.calcadasLivres=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.entradaLivre', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.entradaLivre=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.entradaLivre){
              vm.benfeitoriaClone.edificacao.entradaLivre = angular.copy(vm.benfeitoria.edificacao.entradaLivre);
              $scope.alterados.entradaLivre=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.entradaLivre.id, vm.benfeitoriaClone.edificacao.entradaLivre.id) ?
                $scope.alterados.entradaLivre=$scope.alterados.alterado
                :
                $scope.alterados.entradaLivre=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.pavimentosLivreCadRodas', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.pavimentosLivreCadRodas=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.pavimentosLivreCadRodas){
              vm.benfeitoriaClone.edificacao.pavimentosLivreCadRodas = angular.copy(vm.benfeitoria.edificacao.pavimentosLivreCadRodas);
              $scope.alterados.pavimentosLivreCadRodas=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.pavimentosLivreCadRodas.id, vm.benfeitoriaClone.edificacao.pavimentosLivreCadRodas.id) ?
                $scope.alterados.pavimentosLivreCadRodas=$scope.alterados.alterado
                :
                $scope.alterados.pavimentosLivreCadRodas=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.balcaoInformacoes', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.balcaoInformacoes=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.balcaoInformacoes){
              vm.benfeitoriaClone.edificacao.balcaoInformacoes = angular.copy(vm.benfeitoria.edificacao.balcaoInformacoes);
              $scope.alterados.balcaoInformacoes=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.balcaoInformacoes.id, vm.benfeitoriaClone.edificacao.balcaoInformacoes.id) ?
                $scope.alterados.balcaoInformacoes=$scope.alterados.alterado
                :
                $scope.alterados.balcaoInformacoes=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.pisoTatil', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.pisoTatil=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.pisoTatil){
              vm.benfeitoriaClone.edificacao.pisoTatil = angular.copy(vm.benfeitoria.edificacao.pisoTatil);
              $scope.alterados.pisoTatil=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.pisoTatil.id, vm.benfeitoriaClone.edificacao.pisoTatil.id) ?
                $scope.alterados.pisoTatil=$scope.alterados.alterado
                :
                $scope.alterados.pisoTatil=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.corredorLargo', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.corredorLargo=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.corredorLargo){
              vm.benfeitoriaClone.edificacao.corredorLargo = angular.copy(vm.benfeitoria.edificacao.corredorLargo);
              $scope.alterados.corredorLargo=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.corredorLargo.id, vm.benfeitoriaClone.edificacao.corredorLargo.id) ?
                $scope.alterados.corredorLargo=$scope.alterados.alterado
                :
                $scope.alterados.corredorLargo=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.sanitarioPublico', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.sanitarioPublico=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.sanitarioPublico){
              vm.benfeitoriaClone.edificacao.sanitarioPublico = angular.copy(vm.benfeitoria.edificacao.sanitarioPublico);
              $scope.alterados.sanitarioPublico=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.sanitarioPublico.id, vm.benfeitoriaClone.edificacao.sanitarioPublico.id) ?
                $scope.alterados.sanitarioPublico=$scope.alterados.alterado
                :
                $scope.alterados.sanitarioPublico=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.braile', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.braile || vm.benfeitoria.braile.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.braile={};
            $scope.alterados.braile=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.braile){
              vm.benfeitoriaClone.edificacao.braile = angular.copy(vm.benfeitoria.edificacao.braile);
              $scope.alterados.braile=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.braile.id, vm.benfeitoriaClone.edificacao.braile.id) ?
                $scope.alterados.braile=$scope.alterados.alterado
                :
                $scope.alterados.braile=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.libras', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.libras=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.libras){
              vm.benfeitoriaClone.edificacao.libras = angular.copy(vm.benfeitoria.edificacao.libras);
              $scope.alterados.libras=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.libras.id, vm.benfeitoriaClone.edificacao.libras.id) ?
                $scope.alterados.libras=$scope.alterados.alterado
                :
                $scope.alterados.libras=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.possuiLaudo', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.possuiLaudo=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.possuiLaudo){
              vm.benfeitoriaClone.edificacao.possuiLaudo = angular.copy(vm.benfeitoria.edificacao.possuiLaudo);
              $scope.alterados.possuiLaudo=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.possuiLaudo.id, vm.benfeitoriaClone.edificacao.possuiLaudo.id) ?
                $scope.alterados.possuiLaudo=$scope.alterados.alterado
                :
                $scope.alterados.possuiLaudo=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.linkLaudo', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.linkLaudo=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.linkLaudo){
              vm.benfeitoriaClone.edificacao.linkLaudo = angular.copy(vm.benfeitoria.edificacao.linkLaudo);
              $scope.alterados.linkLaudo=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.linkLaudo.id, vm.benfeitoriaClone.edificacao.linkLaudo.id) ?
                $scope.alterados.linkLaudo=$scope.alterados.alterado
                :
                $scope.alterados.linkLaudo=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.possuiPlanoTrabalho', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.possuiPlanoTrabalho=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.possuiPlanoTrabalho){
              vm.benfeitoriaClone.edificacao.possuiPlanoTrabalho = angular.copy(vm.benfeitoria.edificacao.possuiPlanoTrabalho);
              $scope.alterados.possuiPlanoTrabalho=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.possuiPlanoTrabalho.id, vm.benfeitoriaClone.edificacao.possuiPlanoTrabalho.id) ?
                $scope.alterados.possuiPlanoTrabalho=$scope.alterados.alterado
                :
                $scope.alterados.possuiPlanoTrabalho=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.linkPlanoTrabalho', function(value){
        if(value && $scope.isEditando) {
          if(!vm.benfeitoriaClone.edificacao || vm.benfeitoria.edificacao.semInformacaoAcessibilidade){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.linkPlanoTrabalho=$scope.alterados.alterado;
          }else{
            if(!vm.benfeitoriaClone.edificacao.linkPlanoTrabalho){
              vm.benfeitoriaClone.edificacao.linkPlanoTrabalho = angular.copy(vm.benfeitoria.edificacao.linkPlanoTrabalho);
              $scope.alterados.linkPlanoTrabalho=$scope.alterados.alterado;
            }else{
              !angular.equals(vm.benfeitoria.edificacao.linkPlanoTrabalho.id, vm.benfeitoriaClone.edificacao.linkPlanoTrabalho.id) ?
                $scope.alterados.linkPlanoTrabalho=$scope.alterados.alterado
                :
                $scope.alterados.linkPlanoTrabalho=$scope.alterados.original;
            }
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.planoProtecaoIncendio', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.planoProtecaoIncendio=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.planoProtecaoIncendio,vm.benfeitoriaClone.edificacao.planoProtecaoIncendio) ?
              $scope.alterados.planoProtecaoIncendio=$scope.alterados.alterado
              :
              $scope.alterados.planoProtecaoIncendio=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.habitese', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.habitese=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.habitese,vm.benfeitoriaClone.edificacao.habitese) ?
              $scope.alterados.habitese=$scope.alterados.alterado
              :
              $scope.alterados.habitese=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.alvaraConstrucao', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.alvaraConstrucao=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.alvaraConstrucao,vm.benfeitoriaClone.edificacao.alvaraConstrucao) ?
              $scope.alterados.alvaraConstrucao=$scope.alterados.alterado
              :
              $scope.alterados.alvaraConstrucao=$scope.alterados.original;
          }
        }
      });

      $scope.$watch('vm.benfeitoria.edificacao.laudoInspencao', function(value){
        if(value && $scope.isEditando){
          if(!vm.benfeitoriaClone.edificacao){
            vm.benfeitoriaClone.edificacao={};
            $scope.alterados.laudoInspencao=$scope.alterados.alterado;
          }else{
            !angular.equals(vm.benfeitoria.edificacao.laudoInspencao,vm.benfeitoriaClone.edificacao.laudoInspencao) ?
              $scope.alterados.laudoInspencao=$scope.alterados.alterado
              :
              $scope.alterados.laudoInspencao=$scope.alterados.original;
          }
        }
      });

      if(!$scope.alterados.loadingTemplate){
        if(vm.benfeitoria.tipoBenfeitoria.id!=vm.benfeitoriaClone.tipoBenfeitoria.id){
          $scope.alterados.tipoBenfeitoria=$scope.alterados.alterado;
        }else{
          $scope.alterados.tipoBenfeitoria=$scope.alterados.original;
        }
        if(!angular.equals(vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao,
            vm.benfeitoriaClone.edificacao.tipoEspecializacaoEdificacao)){
          $scope.alterados.tipoEspecializacaoEdificacao=$scope.alterados.alterado;
        }else{
          $scope.alterados.tipoEspecializacaoEdificacao=$scope.alterados.original;
        }
      }

      $scope.habilitaPlanta=function(){
        $scope.addPlanta=true;
      };

      $scope.openPdfPlanta=function($planta){
        if($planta.id!=undefined){
          ExibirPlantaBenfeitoria.visualizarPlanta($planta.id,$planta.nomeArquivo);
        }else{
          var r = new FileReader();
          r.onload = function(){
            fileService.convertStringToFile(r.result,$planta.nomeArquivo);
          }
          r.readAsBinaryString($planta.plantaFile);
        }
      };

      $scope.removerPlantaBenfeitoria=function ($index) {
        vm.benfeitoria.plantas.splice($index,1);
      };

      $scope.adicionaPlantaBenfeitoria=function () {
        UploadPlantaBenfeitoria.upload(vm.planta).then(function(result){
          result.plantaFile=vm.planta.planta;
          result.planta=null;
          vm.benfeitoria.plantas.push(result);
          vm.planta={};
          $scope.addPlanta=false;
          $scope.forms.plantaBenfeitoria.$setUntouched();
        },function(error){
          if(error.data!=undefined){
              MensagensService.exibirMensagemErro(error.data.message);
          }else{
              MensagensService.exibirMensagemErro(error);
          }
        })
      };

      $scope.selectArquivo = function($file) {
        vm.planta.nomeArquivo = $file[0].name;
      };

    }])

})();

(function () {
  "use strict";
  incModalFotos.$inject = ["$mdDialog", "$document", "$log"];
  angular.module("su-cadastro-imovel").directive("incModalFotos", incModalFotos);

  function incModalFotos($mdDialog, $document, $log) {
    return {
      restrict: "E",
      templateUrl: "scripts-cadastro-imovel/app/directives/utils/templates/inc-modal-fotos.html",
      scope: {
        tituloModal: "@",
        fotos: "="
      },
      link: function ($scope) {

        var vm = $scope,
          fotos = [];

        function init() {
          if (!vm.fotos) {
            $log.error("[inc-modal-fotos] - atributo 'fotos' não pode ser " + vm.fotos);
          }

          angular.forEach(vm.fotos, function (foto) {
            var arquivo = foto.imagem || foto;
            arquivo.descricao = foto.descricao
            arquivo.data = foto.data
            fotos.push(arquivo);
          })
        }

        vm.modal = {
          controller: "ModalFotosController",
          controllerAs: "modFtCtrl",
          templateUrl: 'scripts-cadastro-imovel/pages/modal-fotos/modal-fotos.html',
          parent: angular.element($document.body),
          clickOutsideToClose: true,
          locals: {
            fotos: fotos,
            tituloModal: vm.tituloModal
          }
        };

        vm.exibirModal = function (event) {
          vm.modal.targetEvent = event;
          $mdDialog.show(vm.modal);
        };

        init();
      }
    };
  }
})();

(function () {
    "use strict";
    mensagemDirective.$inject = ["$log"];
    angular.module("su-cadastro-imovel").directive("incMensagem", mensagemDirective);

    function mensagemDirective($log) {
        return {
            restrict: "E",
            templateUrl: "scripts-cadastro-imovel/app/directives/utils/templates/inc-mensagem.html",
            transclude: true,
            scope: {
                titulo: "@",
                tipo: "@",
                noIcon: "@?",
                estilo: "@?"
            },
            link: function ($scope, element, attrs) {

                var vm = $scope;

                vm.icone = null;
                vm.classe = null;

                function init() {
                    switch (vm.tipo) {
                        case "alerta":
                            vm.icone = "error_outline";
                            vm.classe = "inc-mensagem-alerta";
                            break;
                        case "erro":
                            vm.icone = "error";
                            vm.classe = "inc-mensagem-erro";
                            break;
                        case "info":
                            vm.icone = "info_outline";
                            vm.classe = "inc-mensagem-info";
                            break;
                        default:
                            $log.error("[inc-mensagem] : Tipo inválido!");
                    }
                }

                vm.showIcon = function () {
                    return !("noIcon" in attrs);
                };

                init();
            }
        };
    }
})();

(function () {
    "use strict";
    angular.module("su-cadastro-imovel").directive("incAjustaTamanho", directive);

    function directive() {
        return {
            restrict: "A",
            require: ["?mdDatepicker", "?mdDatepickerCustom"],
            link: function ($scope, element, attrs, ctrls) {

                angular.element(function () {
                    var ctrl = ctrls[0] || ctrls[1],
                        elemento = angular.element(element[0]),
                        size = 0,
                        label = elemento.parent().find("label")[0],
                        placeholder = ctrl.inputElement.placeholder;

                    if (label && placeholder) {
                        size = label.textContent.length > placeholder.length ? label.textContent.length : placeholder.length;
                    } else if (label) {
                        size = label.textContent.length;
                    } else if (placeholder) {
                        size = placeholder.length;
                    }

                    function resize() {
                        if (size > 0) {
                            ctrl.inputElement.size = size;
                        }
                    }

                    Object.getPrototypeOf(ctrl).resizeInputElement = resize;

                    ctrl.ngInputElement.on("input", angular.bind(ctrl, resize));

                    resize();
                });

            }
        };
    }
})();

(function () {
  "use strict";
  UploadArquivo.$inject = ["$mdDialog", "$document", "NotificadorService", "MAPA_CONSTANTS_INCORPORACAO"];
  angular.module("su-cadastro-imovel").directive("uploadArquivo", UploadArquivo);

  function UploadArquivo($mdDialog, $document, NotificadorService, MAPA_CONSTANTS_INCORPORACAO) {
    return {
      templateUrl: "scripts-cadastro-imovel/app/directives/upload-arquivo/templates/upload-arquivo.html",
      scope: {
        isDisabled: "=",
        arquivo: "=",
        municipio:"=",
        fnLimpar: "&",
        fnSalvar: "&",
        tipoGeometria: "=",
        detail:"="
      },
      link: function ($scope) {
        var vm = $scope,
          notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES;

        vm.arquivo = vm.arquivo || {};

        function getDialogUploadArquivo(ev) {
          return {
            controller: "UploadArquivoController",
            controllerAs: 'uploadCtrl',
            templateUrl: 'scripts-cadastro-imovel/app/directives/upload-arquivo/templates/modal-upload-arquivo.html',
            parent: angular.element($document.body),
            targetEvent: ev,
            clickOutsideToClose: true,
            locals: {
              arquivo: vm.arquivo,
              municipio: vm.municipio,
              detail: vm.detail
            }
          };
        }

        function showDialogUploadArquivo(ev) {
          $mdDialog.show(getDialogUploadArquivo(ev)).then(function (data) {
            // Hide
            vm.fnSalvar({ data: data });
          }, function () {
            // Cancel
            if (!vm.arquivo || !vm.arquivo.arquivo)
              vm.arquivo = {};
          });
        }

        vm.verificarExistenciaDeArquivo = function (ev) {
          // if (vm.arquivo && vm.arquivo.arquivo && vm.arquivo.nome) {
          //   var confirm = $mdDialog.confirm({ focusOnOpen: false })
          //     .title('')
          //     .textContent('Para enviar um novo arquivo será necessário excluir o arquivo existente. Deseja realmente excluir?')
          //     .targetEvent(ev)
          //     .ok('Sim')
          //     .cancel('Não');

          //   $mdDialog.show(confirm).then(
          //     function () {
          //       //SIM
          //       vm.fnLimpar();
          //       vm.arquivo = {};
          //       NotificadorService.notify($scope, vm.tipoGeometria, notifiers.LIMPAR_DESENHO);

          //       showDialogUploadArquivo(ev);
          //     }
          //   );
          // } else {
          showDialogUploadArquivo(ev);
          // }
        };

        vm.excluirArquivo = function (ev) {
          var confirm = $mdDialog.confirm({ focusOnOpen: false })
            .title('')
            .textContent('Se houver no mapa de localização do terreno alguma geometria enviada por meio de um arquivo, essa será excluída. Deseja realmente excluir o arquivo?')
            .targetEvent(ev)
            .ok('Sim')
            .cancel('Não');

          $mdDialog.show(confirm).then(
            function () {
              vm.fnLimpar();
              NotificadorService.notify($scope, vm.tipoGeometria, notifiers.LIMPAR_DESENHO);
            }
          );
        };
      }
    };
  }
})();

(function () {
  'use strict';
  UploadArquivoController.$inject = ["$scope", "$mdDialog", "FormaLevantamento", "TipoEquipamento", "TerrenoService", "MensagensService", "arquivo", "municipio", "detail"];
  angular
    .module('su-cadastro-imovel')
    .controller('UploadArquivoController', UploadArquivoController);

  function UploadArquivoController(
    $scope,
    $mdDialog,
    FormaLevantamento,
    TipoEquipamento,
    TerrenoService,
    MensagensService,
    arquivo,
    municipio,
    detail
  ) {
    var vm = $scope;

    vm.fileUploaded = undefined;
    vm.arquivo = arquivo;
    vm.municipio=municipio;
    vm.tiposEquipamento = TipoEquipamento.query();
    vm.formasLevantamento = FormaLevantamento.query();
    vm.hoje = new Date();
    vm.submittedFormUploadArquivo = false;
    vm.oldArquivo = undefined;
    vm.oldFileUploaded = undefined;
    vm.detail = detail;

    vm.init = function () {
      if (vm.arquivo && vm.arquivo.arquivo) {
        vm.fileUploaded = vm.convertBase64ToFile(vm.arquivo.arquivo, 'zip');
      }
    };

    vm.confirmar = function () {
      vm.submittedFormUploadArquivo = true;
      if (vm.forms.uploadArquivo.$error.required || !vm.fileUploaded) {
        MensagensService.exibirMensagemCamposObrigatorios();
      } else {
        var data = {
          arquivo: vm.fileUploaded,
          dataPlanta: vm.arquivo.dataPlanta,
          escala: vm.arquivo.escala ? vm.arquivo.escala : '',
          tipoFormaLevantamento: vm.arquivo.tipoFormaLevantamento
            ? vm.arquivo.tipoFormaLevantamento.id
            : '',
          resolucaoEspacial: vm.arquivo.resolucaoEspacial || 0,
          tipoEquipamento: vm.arquivo.tipoEquipamento
            ? vm.arquivo.tipoEquipamento.id
            : '',
          codMunicipioImovel: vm.municipio,
        };

        TerrenoService.uploadArquivo(data).then(successCallback, errorCallback);

        function successCallback(response) {
          if (response.data.padraoSirgas === true) {
            var resposta;

            vm.arquivo.nome = response.data.nome;
            vm.arquivo.arquivo = response.data.arquivo;

            resposta = {
              arquivo: vm.arquivo,
              memorialDescritivo: response.data.memorialDescritivoCadastral,
              geometria: response.data.geometry,
              areaTotal: response.data.areaGeometria,
            };
            $mdDialog.hide(resposta);
          }else{
            vm.fileUploaded = undefined;
            MensagensService.exibirMensagemErro("O arquivo shape selecionado não está no padrão SIRGAS 2000 com Projeção Geográfica em graus decimais. Favor selecionar um arquivo nesse padrão.");
          }
        }
        function errorCallback(error) {
          vm.fileUploaded = undefined;
          MensagensService.exibirMensagemErro(error.data.message);
        }
      }
    };

    vm.fechar = function () {
      $mdDialog.cancel();
    };

    vm.handleFormaLevantamentoSelected = function () {
      if (vm.arquivo.tipoFormaLevantamento.id === 3) {
        vm.arquivo.escala = undefined;
      }
    };

    vm.selecionaArquivo = function () {
      if (vm.fileUploaded) {
        if (
          !vm.fileUploaded.name.endsWith('.zip') &&
          !vm.fileUploaded.name.endsWith('.csv') &&
          !vm.fileUploaded.name.endsWith('.txt')
        ) {
          vm.fileUploaded = undefined;
          MensagensService.exibirMensagemErro('Arquivo em formato inválido.');
        }
      }
    };

    vm.download = function () {
      if (vm.arquivo && vm.arquivo.arquivo) {
        var type = vm.arquivo.nome.endsWith('.zip')
          ? 'zip'
          : vm.arquivo.nome.endsWith('.csv')
          ? 'csv'
          : 'txt';
        var a = window.document.createElement('a');
        a.href = 'data:application/' + type + ';base64,' + vm.arquivo.arquivo;
        a.download = vm.arquivo.nome;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    };

    vm.novoArquivo = function () {
      angular.element('#input-file').trigger('click');

      if (!vm.oldArquivo) {
        vm.oldArquivo = vm.arquivo;
        vm.oldFileUploaded = vm.fileUploaded;
        vm.arquivo = undefined;
        vm.fileUploaded = undefined;
      }
    };

    vm.excluirArquivo = function () {
      vm.arquivo = vm.oldArquivo;
      vm.fileUploaded = vm.oldFileUploaded;
      vm.oldFileUploaded = undefined;
      vm.oldArquivo = undefined;
    };

    vm.convertBase64ToFile = function (file, type) {
      var bstr = atob(file),
        n = bstr.length,
        u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new File([u8arr], vm.arquivo.nome, { type: type });
    };

    vm.init();
  }
})();

( function () {
    "use strict";
    angular.module("su-cadastro-imovel").directive("isolateForm", directive);

    function directive() {
        return {
            restrict: 'A',
            require: '?form',
            link: function (scope, elm, attrs, ctrl) {
                if (!ctrl) {
                    return;
                }

                var parentForm = ctrl.$$parentForm;
                if (!parentForm) {
                    return;
                }

                parentForm.$removeControl(ctrl);
            }
        };
    }
})();

(function () {
    angular.module("su-cadastro-imovel").directive("numeroProcessoMask", function(){
        return {
            require : 'ngModel',
            link : function(scope, element, attrs, ngModel){
                ngModel.$parsers.push(function (data) {
                    if(data === null){
                        data = "";
                    }

                    var numeroProcesso = angular.copy(data.replace(/[^\d]+/g,''));
                    var numeroProcessoFormatado = null;

                    var primeiraCessao = numeroProcesso.substr(0, 5);
                    var segundaCessao = numeroProcesso.substr(5, 6);
                    var terceiraCessao = numeroProcesso.substr(11, 4);
                    var quartaCessao = numeroProcesso.substr(15, 2);

                    if(primeiraCessao != "" && segundaCessao != ""){
                        numeroProcessoFormatado = primeiraCessao + "." + segundaCessao;
                    }

                    if(segundaCessao != "" && terceiraCessao != ""){
                        numeroProcessoFormatado = primeiraCessao + "." + segundaCessao + "/" + terceiraCessao;
                    }

                    if(terceiraCessao != "" && quartaCessao != ""){
                        numeroProcessoFormatado = primeiraCessao + "." + segundaCessao + "/" + terceiraCessao + "-" + quartaCessao;

                    }

                    if(!numeroProcessoFormatado){
                        ngModel.$setViewValue(data);
                        ngModel.$render();

                        return data;
                    }else{
                        ngModel.$setViewValue(numeroProcessoFormatado);
                        ngModel.$render();

                        return  numeroProcessoFormatado;
                    }
                });
            }
        };
    });
})();


(function () {
    "use strict";
    /*global ol*/
    incMapaHomologar.$inject = ["ciMapaStyleFactory"];
    angular.module("su-cadastro-imovel").directive("incMapaHomologar", incMapaHomologar);

    function incMapaHomologar(ciMapaStyleFactory) {
        return {
            restrict: "E",
            template: "<div ng-class=\"dadoAlterado ? 'borda-vermelha' : 'borda-cinza'\"></div>",
            scope: {
                geometria: "=?",
                dadoAlterado: "=?"
            },
            link: function ($scope, element) {

                var vm = $scope,
                    geoJsonConverter = new ol.format.GeoJSON(),
                    osm = new ol.layer.Tile({
                        source: new ol.source.OSM()
                    }),
                    imovelSource = new ol.source.Vector(),
                    imovelLayer = new ol.layer.Vector({
                        source: imovelSource,
                        name: 'imovel',
                        required: true
                    }),
                    pointStyle = ciMapaStyleFactory.pinVerde(),
                    polygonStyle = ciMapaStyleFactory.terrenoVerde();

                vm.map = null;

                function init() {

                    vm.map = new ol.Map({
                        target: element[0].firstChild,
                        view: new ol.View({
                            center: [0, 0],
                            zoom: 4,
                            maxZoom: 19,
                            minZoom: 1
                        }),
                        layers: [
                            osm,
                            imovelLayer
                        ]
                    });

                    if (vm.geometria) {
                        var geometria = geoJsonConverter.readGeometry(vm.geometria);
                        var feature = new ol.Feature({
                            geometry: geometria
                        });

                        if (geometria instanceof ol.geom.Point) {
                            feature.setStyle(pointStyle);
                        } else if (geometria instanceof ol.geom.Polygon) {
                            feature.setStyle(polygonStyle);
                        }

                        imovelSource.addFeature(feature);

                        vm.map.getView().fit(geometria.getExtent(), vm.map.getSize());
                    }
                }

                init();
            }
        };
    }
})();

(function () {
  "use strict";
  /*global ol*/
  incMapaHomologarImovelDetalhar.$inject = ["ciMapaViewFactory", "ciMapaLayersFactory", "NotificadorService", "MAPA_CONSTANTS_INCORPORACAO", "ciMapaStyleFactory"];
  angular.module("su-cadastro-imovel").directive("incMapaHomologarImovelDetalhar", incMapaHomologarImovelDetalhar);

  function incMapaHomologarImovelDetalhar(ciMapaViewFactory,
    ciMapaLayersFactory,
    NotificadorService,
    MAPA_CONSTANTS_INCORPORACAO,
    ciMapaStyleFactory) {
    return {
      templateUrl: 'scripts-cadastro-imovel/app/directives/ci-mapa/view/ci-mapa-homologar-template.html',
      scope: {
        showMinimapa: '=showminimapa',
        heightMinimapa: '=heightminimapa',
        widthMinimapa: '=widthminimapa',
        tabIndex: '=tabindex',
        isUnidadeAutonoma: '=isunidadeautonoma',
        imovel: '=',
        action: '@',
        benfeitoria: '=',
        habilitarDesenho: "=?",
        edificacoes: '=',
        geometria: "=?",
      },
      link: function (scope, element) {
        angular.element(document).ready(function () {
          var vm = scope,
            geoJsonConverter = new ol.format.GeoJSON(),
            viewFac = ciMapaViewFactory,
            layersFac = ciMapaLayersFactory,
            imovelSource = new ol.source.Vector,
            pontoSource = new ol.source.Vector,
            unidadeAutonomaSource = new ol.source.Vector,
            terrenoCartorialSource = new ol.source.Vector,
            terrenoCadastralSource = new ol.source.Vector,

            unidadeAutonomaSourceAlterado = new ol.source.Vector,
            terrenoCartorialSourceAlterado = new ol.source.Vector,
            terrenoCadastralSourceAlterado = new ol.source.Vector,
            pontoSourceAlterado = new ol.source.Vector,

            // imovelLayer = new ol.layer.Vector({
            //   source: imovelSource,
            //   name: 'imovel',
            //   required: true
            // }),
            geoJson = new ol.format.GeoJSON({
              defaultProjection: 'EPSG:4674'
            }),
            styles = ciMapaStyleFactory,
            notifiers = MAPA_CONSTANTS_INCORPORACAO.NOTIFICADORES,
            featureIds = MAPA_CONSTANTS_INCORPORACAO.FEATURE_ID,
            terrenoImovel,
            geometriaImovelTerrenoCadastralJson,
            geometriaImovelTerrenoCadastralAlteradoJson,
            geometriaImovelTerrenoCartorialJson,
            geometriaImovelTerrenoCartorialAlteradoJson,
            geometriaImovelTerrenoCadastral,
            geometriaImovelTerrenoCadastralAlterado,
            geometriaImovelTerrenoCartorial,
            geometriaImovelTerrenoCartorialAlterado,
            geometriaImovelPontoJson,
            geometriaImovelPontoAlteradoJson,
            geometriaImovelPonto,
            geometriaImovelPontoAlterado,
            geometriaImovelUnidadeAutonomaAlteradoJson,
            geometriaImovelUnidadeAutonomaAlterado,
            geometriaImovelUnidadeAutonomaJson,
            geometriaImovelUnidadeAutonoma,
            pontoAmareloImovelValidoStyle,
            terrenoValidoStyle,
            terrenoValidoPontilhadoStyle,
            unidadeAutonomaStyle,
            benfeitoriaStyle,
            outrosImoveisStyle,
            terrenoAmareloAlteradoStyle,
            featuresBenfeitoriasAlterado = [],
            featuresBenfeitoriasSource = [],
            featuresOutrosImoveisSource = new ol.source.Vector,
            overlay_,
            pointStyle = ciMapaStyleFactory.pinVerde(),
            polygonStyle = ciMapaStyleFactory.terrenoVerde();

          vm.map = {};
          vm.listaLayers = false;
          vm.bloquearMapa = true;
          vm.bloquearMapaTerreno = false;
          vm.bloquearMapaUnidadeAutonoma = false;
          vm.bloquearMapaBenfeitoria = false;

          function init() {

            vm.map = new ol.Map({
              target: element[0].firstChild,
              view: viewFac.getView(),
              controls: ol.control.defaults({
                attribution: false,
                rotate: false
              }).extend([
                new ol.control.FullScreen({
                  tipLabel: 'Expandir Mapa',
                  className: 'btn-fullscreen',
                  label: '\u2922'
                }),
                new ol.control.ScaleLine(),
                new ol.control.Attribution({
                  className: 'ol-attribution ol-attribution-custom'
                })
              ]),
              interactions: ol.interaction.defaults({
                altShiftDragRotate: false
              }),
              renderer: 'canvas'
            });


            //APROXIMACAO
            if (scope.imovel && scope.imovel.pontoImovel) {
              var geometria = geoJsonConverter.readGeometry(scope.imovel.pontoImovel);
              var feature = new ol.Feature({
                geometry: geometria
              });

              if (geometria instanceof ol.geom.Polygon) {
                feature.setStyle(polygonStyle);
              }

              imovelSource.addFeature(feature);
              vm.map.getView().fit(geometria.getExtent(), vm.map.getSize());
            }

            // vm.map.getLayerGroup().getLayers().insertAt(vm.map.getLayerGroup().getLayers().getArray().length, imovelLayer);

            pontoAmareloImovelValidoStyle = styles.pinAmarelo();
            terrenoValidoStyle = styles.terrenoVerde();
            terrenoAmareloAlteradoStyle = styles.terrenoAmarelo();
            outrosImoveisStyle = styles.terrenoCinzaEscuro();

            vm.map.on("pointermove", function (event) {
              var pixel = vm.map.getEventPixel(event.originalEvent);
              var hit = vm.map.forEachFeatureAtPixel(pixel, function (feature, layer) {
                if (feature.get('name') == featureIds.CENTRO_SEGMENTO) {
                  return true;
                }
              });

              if (hit) {
                vm.map.getTarget().style.cursor = 'pointer';
              } else {
                vm.map.getTarget().style.cursor = '';
              }
            });

            vm.map.getView().on("change:resolution", function () {
              if (vm.map.getView().getZoom() <= 13) {
                removerFeaturesByName(featureIds.CENTRO_SEGMENTO);
              } else if (scope.tabIndex === 1) {
                adicionarCentroSegmentos();
              }
            });

            if (scope.tabIndex === -1) {
              overlay_ = new ol.Overlay({
                element: angular.element(document.querySelector('#overlayLocalizacao'))[0],
                positioning: 'bottom-left'
              });
              vm.map.addOverlay(overlay_);
            }

            vm.map.on('click', function (evt) {
              var feature,
                elemento;

              feature = vm.map.forEachFeatureAtPixel(evt.pixel, function (feature) {
                return feature;
              });

              elemento = overlay_.getElement();
              if (feature) {
                if (feature.get('map_label')) {
                  var RIP = "RIP: ";
                  var MATRICULA = "Matrícula: ";
                  var ENDEREÇO = "Endereço: ";
                  elemento.innerHTML = RIP.bold() + feature.get('map_label') + '<br>'
                    + MATRICULA.bold() + feature.get('map_label_matricula') + '<br>'
                    + ENDEREÇO.bold() + feature.get('map_label_endereco');
                  angular.element(elemento)
                  overlay_.setPosition(evt.coordinate);
                }
              } else {
                overlay_.setPosition(undefined);
                elemento.innerHTML = "";
              }
            });

            /**
             * Imovel Alterado verificar depois para mudar o nome de geometria para imovelAlterado
             */
            if (scope.geometria) {

              geometriaImovelPontoAlteradoJson = scope.geometria.pontoImovel;
              geometriaImovelPontoAlterado = geoJson.readGeometry(geometriaImovelPontoAlteradoJson);

              /**
               * ponto imovel
               */
              if (scope.geometria.pontoImovel) {
                feature = new ol.Feature({
                  geometry: geometriaImovelPontoAlterado
                });
                feature.setId(featureIds.PONTO_IMOVEL_TESTADA);
                feature.setStyle(pontoAmareloImovelValidoStyle);
                pontoSourceAlterado.objetoPreenchido = true
                pontoSourceAlterado.addFeature(feature);
              }

              /**
               * Cadastral
               */

              terrenoImovel = scope.geometria.terreno;
              if (terrenoImovel) {
                geometriaImovelTerrenoCadastralAlteradoJson = terrenoImovel.geometriaTerreno;
                geometriaImovelTerrenoCartorialAlteradoJson = terrenoImovel.geometriaTerrenoCartorial;
                geometriaImovelTerrenoCadastralAlterado = geoJson.readGeometry(geometriaImovelTerrenoCadastralAlteradoJson);
                geometriaImovelTerrenoCartorialAlterado = geoJson.readGeometry(geometriaImovelTerrenoCartorialAlteradoJson);

                if (geometriaImovelTerrenoCadastralAlterado) {
                  feature = new ol.Feature({
                    geometry: geometriaImovelTerrenoCadastralAlterado
                  });
                  feature.setId(featureIds.TERRENO_CADASTRAL_ALTERADO);
                  feature.setStyle(terrenoAmareloAlteradoStyle);
                  feature.set("map_label", scope.geometria.rip);
                  feature.set('map_label_matricula', scope.geometria.incorporacao.numeroMatricula ? scope.geometria.incorporacao.numeroMatricula : '');
                  feature.set('map_label_endereco', (scope.geometria.enderecamento.logradouro ? scope.geometria.enderecamento.logradouro : '')
                    + (scope.geometria.enderecamento.numero ? ' Número: ' + scope.geometria.enderecamento.numero + ' ' : '')
                    + (scope.geometria.enderecamento.bairro ? ' ' + scope.geometria.enderecamento.bairro : '')
                    + (scope.geometria.enderecamento.municipio ? ' ' + scope.geometria.enderecamento.municipio : '')
                    + (scope.geometria.enderecamento.uf ? '/' + scope.geometria.enderecamento.uf : '')
                    + (scope.geometria.enderecamento.pais && scope.geometria.enderecamento.pais.descricao ? ' - ' + scope.geometria.enderecamento.pais.descricao : '')
                    + (scope.geometria.enderecamento.cep ? ' CEP: ' + scope.geometria.enderecamento.cep : ''));
                  terrenoCadastralSourceAlterado.objetoPreenchido = true;
                  terrenoCadastralSourceAlterado.addFeature(feature);
                }

                /**
                 * Cartorial
                 */
                if (geometriaImovelTerrenoCartorialAlterado) {
                  feature = new ol.Feature({
                    geometry: geometriaImovelTerrenoCartorial
                  });
                  feature.setId(featureIds.TERRENO_CARTORIAL_ALTERADO);
                  feature.setStyle(terrenoAmareloAlteradoStyle);
                  terrenoCartorialSourceAlterado.objetoPreenchido = true;
                  terrenoCartorialSourceAlterado.addFeature(feature);
                }
              }
              /**
               * Benfeitorias
               */

              if (scope.geometria.benfeitorias && scope.geometria.benfeitorias.length > 0) {
                angular.forEach(scope.geometria.benfeitorias, function (benfeitoria) {
                  var feature;
                  feature = new ol.Feature({
                    geometry: geoJson.readGeometry(benfeitoria.geometriaBenfeitoria)
                  });
                  feature.setId(benfeitoria.codigo);
                  feature.setStyle(terrenoAmareloAlteradoStyle);
                  feature.set('name', 'benfeitoria');
                  featuresBenfeitoriasAlterado.push(feature);
                });
              }


              /**
               * Unidade autonoma
               */

              if (scope.geometria.unidadeAutonoma && scope.geometria.unidadeAutonoma.edificacao && scope.geometria.unidadeAutonoma.edificacao.geometria) {

                geometriaImovelUnidadeAutonomaAlteradoJson = scope.geometria.unidadeAutonoma.edificacao.geometria;
                geometriaImovelUnidadeAutonomaAlterado = geoJson.readGeometry(geometriaImovelUnidadeAutonomaAlteradoJson);

                feature = new ol.Feature({
                  geometry: geometriaImovelUnidadeAutonomaAlterado
                });
                feature.setId(featureIds.UNIDADE_AUTONOMA_ALTERADO);
                feature.setStyle(terrenoAmareloAlteradoStyle);
                unidadeAutonomaSourceAlterado.objetoPreenchido = true;
                unidadeAutonomaSourceAlterado.addFeature(feature);
              }
            }

            /**
             * Preenchimento do imovel Original
             */
            if (scope.imovel) {
              geometriaImovelPontoJson = scope.imovel.pontoImovel;
              geometriaImovelPonto = geoJson.readGeometry(geometriaImovelPontoJson);
              terrenoImovel = scope.imovel.terreno;

              if (scope.imovel.pontoImovel) {
                feature = new ol.Feature({
                  geometry: geometriaImovelPonto
                });
                feature.setId(featureIds.PONTO_IMOVEL_ORIGINAL);
                feature.setStyle(styles.pinGreenYellow());
                pontoSource.objetoPreenchido = true;
                pontoSource.addFeature(feature);
              }

              if (terrenoImovel) {
                geometriaImovelTerrenoCadastralJson = terrenoImovel.geometriaTerreno;
                geometriaImovelTerrenoCartorialJson = terrenoImovel.geometriaTerrenoCartorial;
                geometriaImovelTerrenoCadastral = geoJson.readGeometry(geometriaImovelTerrenoCadastralJson);
                geometriaImovelTerrenoCartorial = geoJson.readGeometry(geometriaImovelTerrenoCartorialJson);


                if (scope.imovel.terreno.geometriaTerreno) {
                  feature = new ol.Feature({
                    geometry: geometriaImovelTerrenoCadastral
                  });
                  feature.setId(featureIds.TERRENO_CADASTRAL);
                  if (scope.imovel.terreno.geometriaTerrenoCartorial) {
                    feature.setStyle(terrenoValidoPontilhadoStyle);
                  } else {
                    feature.setStyle(terrenoValidoStyle);
                  }
                  feature.set("map_label", scope.imovel.rip);
                  feature.set('map_label_matricula', scope.imovel.incorporacao.numeroMatricula ? scope.imovel.incorporacao.numeroMatricula : '');
                  feature.set('map_label_endereco', (scope.imovel.enderecamento.logradouro ? scope.imovel.enderecamento.logradouro : '')
                    + (scope.imovel.enderecamento.numero ? ' Número: ' + scope.imovel.enderecamento.numero + ' ' : '')
                    + (scope.imovel.enderecamento.bairro ? ' ' + scope.imovel.enderecamento.bairro : '')
                    + (scope.imovel.enderecamento.municipio ? ' ' + scope.imovel.enderecamento.municipio : '')
                    + (scope.imovel.enderecamento.uf ? '/' + scope.imovel.enderecamento.uf : '')
                    + (scope.imovel.enderecamento.pais && scope.imovel.enderecamento.pais.descricao ? ' - ' + scope.imovel.enderecamento.pais.descricao : '')
                    + (scope.imovel.enderecamento.cep ? ' CEP: ' + scope.imovel.enderecamento.cep : ''));
                  terrenoCadastralSource.objetoPreenchido = true;
                  terrenoCadastralSource.addFeature(feature);
                }

                if (scope.imovel.terreno.geometriaTerrenoCartorial) {
                  feature = new ol.Feature({
                    geometry: geometriaImovelTerrenoCartorial
                  });
                  feature.setId(featureIds.TERRENO_CARTORIAL);
                  feature.setStyle(terrenoValidoStyle);
                  terrenoCartorialSource.objetoPreenchido = true;
                  terrenoCartorialSource.addFeature(feature);
                }
              }

              if (scope.imovel.unidadeAutonoma && scope.imovel.unidadeAutonoma.edificacao && scope.imovel.unidadeAutonoma.edificacao.geometria) {

                geometriaImovelUnidadeAutonomaJson = scope.imovel.unidadeAutonoma.edificacao.geometria;
                geometriaImovelUnidadeAutonoma = geoJson.readGeometry(geometriaImovelUnidadeAutonomaJson);

                feature = new ol.Feature({
                  geometry: geometriaImovelUnidadeAutonoma
                });

                feature.setId(featureIds.UNIDADE_AUTONOMA);
                feature.setStyle(polygonStyle);
                unidadeAutonomaSource.objetoPreenchido = true;
                unidadeAutonomaSource.addFeature(feature);
              }

              if (scope.imovel.benfeitorias && scope.imovel.benfeitorias.length > 0) {
                angular.forEach(scope.imovel.benfeitorias, function (benfeitoria) {
                  var feature;
                  feature = new ol.Feature({
                    geometry: geoJson.readGeometry(benfeitoria.geometriaBenfeitoria)
                  });
                  feature.setId(benfeitoria.codigo);
                  feature.setStyle(polygonStyle);
                  feature.set('name', 'benfeitoria');
                  featuresBenfeitoriasSource.push(feature);
                });
              }
            }

            if (vm.edificacoes && vm.edificacoes.length > 0) {
              angular.forEach(vm.edificacoes, function (imovelDaUF) {
                var feature;
                feature = new ol.Feature({
                  geometry: geoJson.readGeometry(imovelDaUF.terreno.geometriaTerreno)
                });
                feature.setId(imovelDaUF.id);
                feature.setStyle(outrosImoveisStyle);
                feature.set('name', 'ImovelDaUf');
                feature.set("map_label", imovelDaUF.rip);
                feature.set('map_label_matricula', imovelDaUF.incorporacao.numeroMatricula ? imovelDaUF.incorporacao.numeroMatricula : '');
                feature.set('map_label_endereco', (imovelDaUF.enderecamento.logradouro ? imovelDaUF.enderecamento.logradouro : '')
                  + (imovelDaUF.enderecamento.numero ? ' Número: ' + imovelDaUF.enderecamento.numero + ' ' : '')
                  + (imovelDaUF.enderecamento.bairro ? ' ' + imovelDaUF.enderecamento.bairro : '')
                  + (imovelDaUF.enderecamento.municipio ? ' ' + imovelDaUF.enderecamento.municipio : '')
                  + (imovelDaUF.enderecamento.uf ? '/' + imovelDaUF.enderecamento.uf : '')
                  + (imovelDaUF.enderecamento.pais && imovelDaUF.enderecamento.pais.descricao ? ' - ' + imovelDaUF.enderecamento.pais.descricao : '')
                  + (imovelDaUF.enderecamento.cep ? ' CEP: ' + imovelDaUF.enderecamento.cep : ''));
                  featuresOutrosImoveisSource.addFeature(feature);
              });
            }

            var imovelFinal = {
              rip: vm.imovel.rip,
              pontoSourceAlterado: pontoSourceAlterado,
              terrenoCartorialSourceAlterado: terrenoCartorialSourceAlterado,
              terrenoCadastralSourceAlterado: terrenoCadastralSourceAlterado,
              featuresBenfeitoriasAlterado: featuresBenfeitoriasAlterado,
              unidadeAutonomaSourceAlterado: unidadeAutonomaSourceAlterado,

              pontoSource: pontoSource,
              unidadeAutonomaSource: unidadeAutonomaSource,
              terrenoCartorialSource: terrenoCartorialSource,
              terrenoCadastralSource: terrenoCadastralSource,
              featuresBenfeitoriasSource: featuresBenfeitoriasSource,

              featuresOutrosImoveisSource: featuresOutrosImoveisSource
            }

            vm.layers = layersFac.getCamadasHomologar(imovelFinal);
            angular.forEach(vm.layers, function (layer) {
              vm.map.addLayer(layer);
            });
          }

          vm.alterarBingVisible = function () {
            angular.forEach(vm.map.getLayers(), function (value) {
              if (value.get('name') === 'bingMapa') {
                value.setVisible(!value.getVisible());
                NotificadorService.notify(scope, value.getVisible(), notifiers.ALTERAR_VISIBILIDADE_BING);
              }
            });

          };

          vm.apresentaLabelOriginal = function (){
              return vm.imovel.rip != null;
          }

          vm.alterarLayerVisible = function (name) {
            angular.forEach(vm.map.getLayers(), function (value) {
              if (value.get('name') === name.get('name')) {
                value.setVisible(!value.getVisible());
              }
            });
          };

          vm.layerFilter = function (layer) {
            return !layer.get('required') && layer.condicaoImovel == 'O';
          };

          vm.layerFilterImovelAlterado = function (layer) {
            return !layer.get('required') && layer.condicaoImovel == 'A';
          };

          vm.alterarVisibilidadeBalaoCamadas = function () {
            vm.listaLayers = !vm.listaLayers;
          };

          init();
        });

      }
    };
  }
})();

(function () {
    "use strict";
    incHomologarDadosTituloAquisitivo.$inject = ["ArquivoService"];
    angular.module("su-cadastro-imovel").directive("incHomologarDadosTituloAquisitivo", incHomologarDadosTituloAquisitivo);

    function incHomologarDadosTituloAquisitivo(ArquivoService) {
        return {
            restrict: "E",
            templateUrl: "scripts-cadastro-imovel/app/directives/homologar/templates/inc-homologar-dados-titulo-aquisitivo.html",
            scope: {
                titulos: "=",
                index: "=",
                fnIsCadastroAlterado: "&",
                fnDesabilitarCheckbox: "&",
                fnIsDadoDivergente: "&",
                exists: "&",
                toggle: "&"
            },
            link: function ($scope) {

                var vm = $scope;

                vm.encargos = [];

                function init() {

                    gerarUrlArquivos();
                    extrairEncargos();
                }

                function gerarUrlArquivos() {
                    if (vm.titulos.tituloOriginal) {
                        vm.titulos.tituloOriginal.url = ArquivoService.getUrlFromBase64(vm.titulos.tituloOriginal.arquivo);
                    }

                    if (vm.titulos.tituloAlterado) {
                        vm.titulos.tituloAlterado.url = ArquivoService.getUrlFromBase64(vm.titulos.tituloAlterado.arquivo);
                    }
                }

                function extrairEncargos() {
                    if (vm.titulos.tituloOriginal) {
                        for (var i=0; i<vm.titulos.tituloOriginal.encargos.length; i++) {
                            var encargo = {
                                encargoOriginal: vm.titulos.tituloOriginal.encargos[i],
                                encargoAlterado: null
                            };

                            var nome = vm.titulos.tituloOriginal.encargos[i].descricao;

                            if (vm.titulos.tituloAlterado) {
                                for (var j=0; j<vm.titulos.tituloAlterado.encargos.length; j++) {
                                    if (nome === vm.titulos.tituloAlterado.encargos[j].descricao) {
                                        encargo.encargoAlterado = vm.titulos.tituloAlterado.encargos[j];
                                        break;
                                    }
                                }
                            }

                            vm.encargos.push(encargo);
                        }
                    }

                    if (vm.titulos.tituloAlterado) {
                        for (i=0; i<vm.titulos.tituloAlterado.encargos.length; i++) {
                            encargo = {
                                encargoOriginal: null,
                                encargoAlterado: vm.titulos.tituloAlterado.encargos[i]
                            };

                            nome = vm.titulos.tituloAlterado.encargos[i].descricao;

                            if (!vm.titulos.tituloOriginal || !contem(vm.titulos.tituloOriginal.encargos, "descricao", nome)) {
                                vm.encargos.push(encargo);
                            }
                        }
                    }
                }

                function contem(objectArray, attribute, item) {
                    var retorno = false;

                    for (var i=0; i<objectArray.length; i++) {
                        if (item === objectArray[i][attribute]) {
                            retorno = true;
                        }
                    }

                    return retorno;
                }

                vm.isCadastroAlterado = function () {
                    return vm.fnIsCadastroAlterado();
                };

                vm.isChecked = function (campo) {
                    campo += vm.index;
                    return vm.exists({campo: campo});
                };

                vm.alternar =function (element) {
                    vm.toggle({element: element});
                };

                vm.desabilitarCheckbox = function (dadoOriginal, dadoAlterado) {
                    return vm.fnDesabilitarCheckbox({dadoOriginal: dadoOriginal, dadoAlterado: dadoAlterado});
                };

                vm.isDadoDivergente = function (dadoOriginal, dadoAlterado) {
                    return vm.fnIsDadoDivergente({dadoOriginal: dadoOriginal, dadoAlterado: dadoAlterado});
                };

                init();
            }
        };
    }
})();

(function () {
    "use strict";
    angular.module("su-cadastro-imovel").directive("incHomologarDadosMedidor", incHomologarDadosMedidor);

    function incHomologarDadosMedidor() {
        return {
            restrict: "E",
            templateUrl: "scripts-cadastro-imovel/app/directives/homologar/templates/inc-homologar-dados-medidor.html",
            scope: {
                medidores: "=",
                index: "=",
                fnIsCadastroAlterado: "&",
                fnDesabilitarCheckbox: "&",
                fnIsDadoDivergente: "&",
                exists: "&",
                toggle: "&"
            },
            link: function ($scope) {

                var vm = $scope;

                vm.isCadastroAlterado = function () {
                    return vm.fnIsCadastroAlterado();
                };

                vm.isChecked = function (campo) {
                    campo += vm.index;
                    return vm.exists({campo: campo});
                };

                vm.alternar =function (element) {
                    vm.toggle({element: element});
                };

                vm.desabilitarCheckbox = function (dadoOriginal, dadoAlterado) {
                    return vm.fnDesabilitarCheckbox({dadoOriginal: dadoOriginal, dadoAlterado: dadoAlterado});
                };

                vm.isDadoDivergente = function (dadoOriginal, dadoAlterado) {
                    return vm.fnIsDadoDivergente({dadoOriginal: dadoOriginal, dadoAlterado: dadoAlterado});
                };
            }
        };
    }
})();

(function () {
    "use strict";
    angular.module("su-cadastro-imovel").directive("incHomologarDadosMatricula", incHomologarDadosMatricula);

    function incHomologarDadosMatricula() {
        return {
            restrict: "E",
            templateUrl: "scripts-cadastro-imovel/app/directives/homologar/templates/inc-homologar-dados-matricula.html",
            scope: {
                matriculas: "=",
                index: "=",
                fnIsCadastroAlterado: "&",
                fnDesabilitarCheckbox: "&",
                fnIsDadoDivergente: "&",
                exists: "&",
                toggle: "&"
            },
            link: function ($scope) {

                var vm = $scope;

                vm.isCadastroAlterado = function () {
                    return vm.fnIsCadastroAlterado();
                };

                vm.isChecked = function (campo) {
                    campo += vm.index;
                    return vm.exists({campo: campo});
                };

                vm.alternar =function (element) {
                    vm.toggle({element: element});
                };

                vm.desabilitarCheckbox = function (dadoOriginal, dadoAlterado) {
                    return vm.fnDesabilitarCheckbox({dadoOriginal: dadoOriginal, dadoAlterado: dadoAlterado});
                };

                vm.isDadoDivergente = function (dadoOriginal, dadoAlterado) {
                    return vm.fnIsDadoDivergente({dadoOriginal: dadoOriginal, dadoAlterado: dadoAlterado});
                };

                vm.isEquals = function(dadoOriginal, dadoAlterado) {
                    if (dadoAlterado instanceof Object) {
                        return angular.equals(dadoOriginal, dadoAlterado);
                    } else {
                        return dadoOriginal == dadoAlterado;
                    }
                };
            }
        };
    }
})();

(function () {
    "use strict";
    angular.module("su-cadastro-imovel").directive("incHomologarDadosEncargo", incHomologarDadosEncargo);

    function incHomologarDadosEncargo() {
        return {
            restrict: "E",
            templateUrl: "scripts-cadastro-imovel/app/directives/homologar/templates/inc-homologar-dados-encargo.html",
            scope: {
                encargos: "=",
                index: "=",
                fnIsCadastroAlterado: "&",
                fnDesabilitarCheckbox: "&",
                fnIsDadoDivergente: "&",
                exists: "&",
                toggle: "&"
            },
            link: function ($scope) {

                var vm = $scope;

                vm.isCadastroAlterado = function () {
                    return vm.fnIsCadastroAlterado();
                };

                vm.isChecked = function (campo) {
                    campo += vm.index;
                    return vm.exists({campo: campo});
                };

                vm.alternar = function (element) {
                    vm.toggle({element: element});
                };

                vm.desabilitarCheckbox = function (dadoOriginal, dadoAlterado) {
                    return vm.fnDesabilitarCheckbox({dadoOriginal: dadoOriginal, dadoAlterado: dadoAlterado});
                };

                vm.isDadoDivergente = function (dadoOriginal, dadoAlterado) {
                    return vm.fnIsDadoDivergente({dadoOriginal: dadoOriginal, dadoAlterado: dadoAlterado});
                };
            }
        };
    }
})();

(function () {
    "use strict";
    incHomologarDadosBenfeitoria.$inject = ["ArquivoService"];
    angular.module("su-cadastro-imovel").directive("incHomologarDadosBenfeitoria", incHomologarDadosBenfeitoria);

    function incHomologarDadosBenfeitoria(ArquivoService) {
        return {
            restrict: "E",
            templateUrl: "scripts-cadastro-imovel/app/directives/homologar/templates/inc-homologar-dados-benfeitoria.html",
            scope: {
                benfeitorias: "=",
                fnIsCadastroAlterado: "&",
                fnDesabilitarCheckbox: "&",
                fnIsDadoDivergente: "&",
                exists: "&",
                toggle: "&"
            },
            link: function ($scope) {

                var vm = $scope,
                    EDIFICACAO = 1;

                vm.codigo = null;
                vm.medidores = [];

                function init() {

                    vm.codigo = vm.benfeitorias.benfeitoriaOriginal ? vm.benfeitorias.benfeitoriaOriginal.codigo : vm.benfeitorias.benfeitoriaAlterada.codigo;

                    gerarUrlDocumentos();
                    gerarUrlPlantas();
                    extrairMedidores();
                }

                function extrairMedidores() {
                    if (isEdificacao()) {
                        for (var i = 0; i < vm.benfeitorias.benfeitoriaOriginal.edificacao.dadosMedidor.length; i++) {
                            var codigo = vm.benfeitorias.benfeitoriaOriginal.edificacao.dadosMedidor[i].codigo;

                            var medidor = {
                                medidorOriginal: vm.benfeitorias.benfeitoriaOriginal.edificacao.dadosMedidor[i],
                                medidorAlterado: getByAttribute(vm.benfeitorias.benfeitoriaAlterada.edificacao.dadosMedidor, "codigo", codigo)
                            };

                            vm.medidores.push(medidor);
                        }

                        for (i = 0; i < vm.benfeitorias.benfeitoriaAlterada.edificacao.dadosMedidor.length; i++) {
                            medidor = {
                                medidorOriginal: null,
                                medidorAlterado: vm.benfeitorias.benfeitoriaAlterada.edificacao.dadosMedidor[i]
                            };

                            codigo = vm.benfeitorias.benfeitoriaAlterada.edificacao.dadosMedidor[i].codigo;

                            if (!contem(vm.benfeitorias.benfeitoriaOriginal.edificacao.dadosMedidor, "codigo", codigo)) {
                                vm.medidores.push(medidor);
                            }
                        }
                    }
                }

                function getByAttribute(objectArray, attribute, item) {
                    var retorno = null;

                    for (var i = 0; i < objectArray.length; i++) {
                        if (item === objectArray[i][attribute]) {
                            retorno = objectArray[i];
                            break;
                        }
                    }

                    return retorno;
                }

                function contem(objectArray, attribute, item) {
                    var retorno = false;

                    for (var i=0; i<objectArray.length; i++) {
                        if (item === objectArray[i][attribute]) {
                            retorno = true;
                        }
                    }

                    return retorno;
                }

                function isEdificacao() {
                    return vm.benfeitorias.benfeitoriaOriginal.tipoBenfeitoria.id === EDIFICACAO && vm.benfeitorias.benfeitoriaAlterada.tipoBenfeitoria.id === EDIFICACAO;
                }

                function gerarUrlDocumentos() {
                    if (isEdificacao()) {
                        angular.forEach(vm.benfeitorias.benfeitoriaOriginal.edificacao.documentacao, function (documento) {
                            if (documento.arquivo) {
                                documento.arquivo.url = ArquivoService.getUrlFromBase64(documento.arquivo.arquivo);
                            }
                        });

                        angular.forEach(vm.benfeitorias.benfeitoriaAlterada.edificacao.documentacao, function (documento) {
                            if (documento.arquivo) {
                                documento.arquivo.url = ArquivoService.getUrlFromBase64(documento.arquivo.arquivo);
                            }
                        });
                    }
                }

                function gerarUrlPlantas() {
                    angular.forEach(vm.benfeitorias.benfeitoriaOriginal.plantas, function (planta) {
                        planta.url = ArquivoService.getUrlFromBase64(planta.planta);
                    });

                    angular.forEach(vm.benfeitorias.benfeitoriaAlterada.plantas, function (planta) {
                        planta.url = ArquivoService.getUrlFromBase64(planta.planta);
                    });
                }

                vm.isCadastroAlterado = function () {
                    return vm.fnIsCadastroAlterado();
                };

                vm.isChecked = function (campo) {
                    campo += vm.codigo;
                    return vm.exists({campo: campo});
                };

                vm.alternar =function (element) {
                    vm.toggle({element: element});
                };

                vm.desabilitarCheckbox = function (dadoOriginal, dadoAlterado) {
                    return vm.fnDesabilitarCheckbox({dadoOriginal: dadoOriginal, dadoAlterado: dadoAlterado});
                };

                vm.isDadoDivergente = function (dadoOriginal, dadoAlterado) {
                    return vm.fnIsDadoDivergente({dadoOriginal: dadoOriginal, dadoAlterado: dadoAlterado});
                };

                init();
            }
        };
    }
})();

(function () {
    "use strict";
    directive.$inject = ["NotificadorService", "NOTIFICADORES"];
    angular.module("su-cadastro-imovel").directive("ciInputFile", directive);

    function directive(NotificadorService, NOTIFICADORES) {
        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/custom-input-file/template/custom-input-file-tmpl.html",
            scope: {
                model: "=",
                objeto: "=",
                desabilitado: "=",
                cifOnchange: "&",
                cifOnremove: "&",
                accept: "@?",
                name: "@?",
                obrigatorio: "=?",
                botao: "=",
                showInput: "=?"
            },
            link: function ($scope, el) {
                var vm = $scope,
                    input;

                function init(){
                    input = angular.element(angular.element(el).find("input")[0]);
                    input.on("change", function (ev) {
                        vm.$apply(function () {
                            vm.model = ev.target.files[0];
                        });
                        vm.cifOnchange();
                    });
                }

                vm.$watch(
                    function () {
                        return vm.model;
                    },
                    function (newValue) {
                        if (!newValue) {
                            input.val(null);
                        }
                    }
                );

                vm.upload = function () {
                    input.click();
                };
                vm.remover = function () {
                    vm.model = null;
                    vm.objeto = null;
                    vm.cifOnremove();
                };

                vm.baixarDocumento = function () {
                    var documento = vm.objeto;
                    if (documento && documento.arquivo) {
                        var link = document.createElement('a');
                        var url = window.URL || window.webkitURL;
                        var blob = new Blob([base64ToArrayBuffer(documento.arquivo)], {type: 'application/octet-stream'});
                        link.href = url.createObjectURL(blob);
                        var fileName = documento.nomeOriginal;
                        link.download = fileName;
                        link.click();
                    }
                }

                function base64ToArrayBuffer (base64) {
                    var binaryString = window.atob(base64);
                    var binaryLen = binaryString.length;
                    var bytes = new Uint8Array(binaryLen);
                    for (var i = 0; i < binaryLen; i++) {
                        var ascii = binaryString.charCodeAt(i);
                        bytes[i] = ascii;
                    }
                    return bytes;
                }

                vm.nameIsDefined  = function () {
                    if (vm.model && !isEmpty(vm.model.nomeOriginal)) {
                        return vm.model.nomeOriginal;
                    }
                    if (vm.model && !isEmpty(vm.model.name)) {
                        return vm.model.name;
                    }

                    if(vm.objeto && vm.objeto.nomeOriginal) {
                        return vm.objeto.nomeOriginal
                    }
                    return false;
                };

                function isEmpty(value) {
                    return angular.equals({}, value) || angular.isUndefined(value) || value === null || value === "";
                }

                NotificadorService.subscribe(vm, function(evento, escopo, dado) {
                    vm.model = null;
                }, NOTIFICADORES.LIMPAR_ARQUIVO);

                init();
            }
        };
    }
})();

/*
 * Angular Material Data Table
 * https://github.com/daniel-nagy/md-data-table
 * @license MIT
 * v0.10.4

 * NÃO ALTERAR OU APAGAR ESTE ARQUIVO.
 * NECESSÁRIO PARA TRADUZIR OS COMPONENTES DE PAGINAÇÃO
 * @RAPHAEL.SOUZA
*/

(function (window, angular, undefined) {
'use strict';

angular.module('md.table.templates', ['md-table-pagination.html', 'md-table-progress.html', 'arrow-up.svg', 'navigate-before.svg', 'navigate-first.svg', 'navigate-last.svg', 'navigate-next.svg']);

angular.module('md-table-pagination.html', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('md-table-pagination.html',
    '<div class="page-select" ng-if="$pagination.showPageSelect()">\n' +
    '  <div class="label">{{$pagination.label.page}}</div>\n' +
    '\n' +
    '  <md-select virtual-page-select total="{{$pagination.pages()}}" class="md-table-select" ng-model="$pagination.page" md-container-class="md-pagination-select" ng-change="$pagination.onPaginationChange()" ng-disabled="$pagination.disabled" aria-label="Page">\n' +
    '    <md-content>\n' +
    '      <md-option ng-repeat="page in $pageSelect.pages" ng-value="page">{{page}}</md-option>\n' +
    '    </md-content>\n' +
    '  </md-select>\n' +
    '</div>\n' +
    '\n' +
    '<div class="limit-select" ng-if="$pagination.limitOptions">\n' +
    '  <div class="label">{{$pagination.label.rowsPerPage}}</div>\n' +
    '\n' +
    '  <md-select class="md-table-select" ng-model="$pagination.limit" md-container-class="md-pagination-select" ng-disabled="$pagination.disabled" aria-label="Rows" placeholder="{{ $pagination.limitOptions[0] }}">\n' +
    '    <md-option ng-repeat="rows in $pagination.limitOptions" ng-value="rows">{{rows}}</md-option>\n' +
    '  </md-select>\n' +
    '</div>\n' +
    '\n' +
    '<div class="buttons">\n' +
    '  <div class="label">{{$pagination.min()}} - {{$pagination.max()}} {{$pagination.label.of}} {{$pagination.total}}</div>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-if="$pagination.showBoundaryLinks()" ng-click="$pagination.first()" ng-disabled="$pagination.disabled || !$pagination.hasPrevious()" aria-label="First">\n' +
    '    <md-icon md-svg-icon="navigate-first.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-click="$pagination.previous()" ng-disabled="$pagination.disabled || !$pagination.hasPrevious()" aria-label="Previous">\n' +
    '    <md-icon md-svg-icon="navigate-before.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-click="$pagination.next()" ng-disabled="$pagination.disabled || !$pagination.hasNext()" aria-label="Next">\n' +
    '    <md-icon md-svg-icon="navigate-next.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-if="$pagination.showBoundaryLinks()" ng-click="$pagination.last()" ng-disabled="$pagination.disabled || !$pagination.hasNext()" aria-label="Last">\n' +
    '    <md-icon md-svg-icon="navigate-last.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '</div>');
}]);

angular.module('md-table-progress.html', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('md-table-progress.html',
    '<tr>\n' +
    '  <th colspan="{{columnCount()}}">\n' +
    '    <md-progress-linear ng-show="deferred()" md-mode="indeterminate"></md-progress-linear>\n' +
    '  </th>\n' +
    '</tr>');
}]);

angular.module('arrow-up.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('arrow-up.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"/></svg>');
}]);

angular.module('navigate-before.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-before.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>');
}]);

angular.module('navigate-first.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-first.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M7 6 v12 h2 v-12 h-2z M17.41 7.41L16 6l-6 6 6 6 1.41-1.41L12.83 12z"/></svg>');
}]);

angular.module('navigate-last.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-last.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M15 6 v12 h2 v-12 h-2z M8 6L6.59 7.41 11.17 12l-4.58 4.59L8 18l6-6z"/></svg>');
}]);

angular.module('navigate-next.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-next.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>');
}]);


angular.module('md.data.table', ['md.table.templates']);

angular.module('md.data.table').directive('mdBody', mdBody);

function mdBody() {

  function compile(tElement) {
    tElement.addClass('md-body');
  }

  return {
    compile: compile,
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdCell', mdCell);

function mdCell() {
  
  function compile(tElement) {
    var select = tElement.find('md-select');
    
    if(select.length) {
      select.addClass('md-table-select').attr('md-container-class', 'md-table-select');
    }
    
    tElement.addClass('md-cell');
    
    return postLink;
  }
  
  // empty controller to be bind properties to in postLink function
  function Controller() {
    
  }
  
  function postLink(scope, element, attrs, ctrls) {
    var select = element.find('md-select');
    var cellCtrl = ctrls.shift();
    var tableCtrl = ctrls.shift();
    
    if(attrs.ngClick) {
      element.addClass('md-clickable');
    }
    
    if(select.length) {
      select.on('click', function (event) {
        event.stopPropagation();
      });
      
      element.addClass('md-clickable').on('click', function (event) {
        event.stopPropagation();
        select[0].click();
      });
    }
    
    cellCtrl.getTable = tableCtrl.getElement;
    
    function getColumn() {
      return tableCtrl.$$columns[getIndex()];
    }
    
    function getIndex() {
      return Array.prototype.indexOf.call(element.parent().children(), element[0]);
    }
    
    scope.$watch(getColumn, function (column) {
      if(!column) {
        return;
      }
      
      if(column.numeric) {
        element.addClass('md-numeric');
      } else {
        element.removeClass('md-numeric');
      }
    });
  }
  
  return {
    controller: Controller,
    compile: compile,
    require: ['mdCell', '^^mdTable'],
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdColumn', mdColumn);

function mdColumn($compile, $mdUtil) {
  
  function compile(tElement) {
    tElement.addClass('md-column');
    return postLink;
  }

  function postLink(scope, element, attrs, ctrls) {
    var headCtrl = ctrls.shift();
    var tableCtrl = ctrls.shift();
    
    function attachSortIcon() {
      var sortIcon = angular.element('<md-icon md-svg-icon="arrow-up.svg">');
      
      $compile(sortIcon.addClass('md-sort-icon').attr('ng-class', 'getDirection()'))(scope);
      
      if(element.hasClass('md-numeric')) {
        element.prepend(sortIcon);
      } else {
        element.append(sortIcon);
      }
    }
    
    function detachSortIcon() {
      Array.prototype.some.call(element.find('md-icon'), function (icon) {
        return icon.classList.contains('md-sort-icon') && element[0].removeChild(icon);
      });
    }
    
    function disableSorting() {
      detachSortIcon();
      element.removeClass('md-sort').off('click', setOrder);
    }
    
    function enableSorting() {
      attachSortIcon();
      element.addClass('md-sort').on('click', setOrder);
    }
    
    function getIndex() {
      return Array.prototype.indexOf.call(element.parent().children(), element[0]);
    }
    
    function isActive() {
      return scope.orderBy && (headCtrl.order === scope.orderBy || headCtrl.order === '-' + scope.orderBy);
    }
    
    function isNumeric() {
      return attrs.mdNumeric === '' || scope.numeric;
    }
    
    function setOrder() {
      scope.$applyAsync(function () {
        if(isActive()) {
          headCtrl.order = scope.getDirection() === 'md-asc' ? '-' + scope.orderBy : scope.orderBy;
        } else {
          headCtrl.order = scope.getDirection() === 'md-asc' ? scope.orderBy : '-' + scope.orderBy;
        }
        
        if(angular.isFunction(headCtrl.onReorder)) {
          $mdUtil.nextTick(function () {
            headCtrl.onReorder(headCtrl.order);
          });
        }
      });
    }
    
    function updateColumn(index, column) {
      tableCtrl.$$columns[index] = column;
      
      if(column.numeric) {
        element.addClass('md-numeric');
      } else {
        element.removeClass('md-numeric');
      }
    }
    
    scope.getDirection = function () {
      if(isActive()) {
        return headCtrl.order.charAt(0) === '-' ? 'md-desc' : 'md-asc';
      }
      
      return attrs.mdDesc === '' || scope.$eval(attrs.mdDesc) ? 'md-desc' : 'md-asc';
    };
    
    scope.$watch(isActive, function (active) {
      if(active) {
        element.addClass('md-active');
      } else {
        element.removeClass('md-active');
      }
    });
    
    scope.$watch(getIndex, function (index) {
      updateColumn(index, {'numeric': isNumeric()});
    });
    
    scope.$watch(isNumeric, function (numeric) {
      updateColumn(getIndex(), {'numeric': numeric});
    });
    
    scope.$watch('orderBy', function (orderBy) {
      if(orderBy) {
        enableSorting();
      } else {
        disableSorting();
      }
    });
  }

  return {
    compile: compile,
    require: ['^^mdHead', '^^mdTable'],
    restrict: 'A',
    scope: {
      numeric: '=?mdNumeric',
      orderBy: '@?mdOrderBy'
    }
  };
}

mdColumn.$inject = ['$compile', '$mdUtil'];

angular.module('md.data.table')
  .decorator('$controller', controllerDecorator)
  .factory('$mdEditDialog', mdEditDialog);

/*
 * A decorator for ng.$controller to optionally bind properties to the
 * controller before invoking the constructor. Stolen from the ngMock.
 *
 * https://docs.angularjs.org/api/ngMock/service/$controller
 */
function controllerDecorator($delegate) {
  return function(expression, locals, later, ident) {
    if(later && typeof later === 'object') {
      var create = $delegate(expression, locals, true, ident);
      angular.extend(create.instance, later);
      return create();
    }
    return $delegate(expression, locals, later, ident);
  };
}

controllerDecorator.$inject = ['$delegate'];
  
function mdEditDialog($compile, $controller, $document, $mdUtil, $q, $rootScope, $templateCache, $templateRequest, $window) {
  /* jshint validthis: true */
  
  var ESCAPE = 27;
  
  var busy = false;
  var body = angular.element($document.prop('body'));
  
  /*
   * bindToController
   * controller
   * controllerAs
   * locals
   * resolve
   * scope
   * targetEvent
   * template
   * templateUrl
   */
  var defaultOptions = {
    clickOutsideToClose: true,
    disableScroll: true,
    escToClose: true,
    focusOnOpen: true
  };
  
  function build(template, options) {
    var scope = $rootScope.$new();
    var element = $compile(template)(scope);
    var backdrop = $mdUtil.createBackdrop(scope, 'md-edit-dialog-backdrop');
    var controller;
    
    if(options.controller) {
      controller = getController(options, scope, {$element: element, $scope: scope});
    } else {
      angular.extend(scope, options.scope);
    }
    
    if(options.disableScroll) {
      disableScroll(element);
    }
    
    body.prepend(backdrop).append(element.addClass('md-whiteframe-1dp'));
    
    positionDialog(element, options.target);
    
    if(options.focusOnOpen) {
      focusOnOpen(element);
    }
    
    if(options.clickOutsideToClose) {
      backdrop.on('click', function () {
        element.remove();
      });
    }
    
    if(options.escToClose) {
      escToClose(element);
    }
    
    element.on('$destroy', function () {
      busy = false;
      backdrop.remove();
    });
    
    return controller;
  }
  
  function disableScroll(element) {
    var restoreScroll = $mdUtil.disableScrollAround(element, body);
    
    element.on('$destroy', function () {
      restoreScroll();
    });
  }
  
  function getController(options, scope, inject) {
    if(!options.controller) {
      return;
    }
    
    if(options.resolve) {
      angular.extend(inject, options.resolve);
    }
    
    if(options.locals) {
      angular.extend(inject, options.locals);
    }
    
    if(options.controllerAs) {
      scope[options.controllerAs] = {};
      
      if(options.bindToController) {
        angular.extend(scope[options.controllerAs], options.scope);
      } else {
        angular.extend(scope, options.scope);
      }
    } else {
      angular.extend(scope, options.scope);
    }
    
    if(options.bindToController) {
      return $controller(options.controller, inject, scope[options.controllerAs]);
    } else {
      return $controller(options.controller, inject);
    }
  }
  
  function getTemplate(options) {
    return $q(function (resolve, reject) {
      var template = options.template;
      
      function illegalType(type) {
        reject('Unexpected template value. Expected a string; received a ' + type + '.');
      }
      
      if(template) {
        return angular.isString(template) ? resolve(template) : illegalType(typeof template);
      }
      
      if(options.templateUrl) {
        template = $templateCache.get(options.templateUrl);
        
        if(template) {
          return resolve(template);
        }
        
        var success = function (template) {
          return resolve(template);
        };
        
        var error = function () {
          return reject('Error retrieving template from URL.');
        };
        
        return $templateRequest(options.templateUrl).then(success, error);
      }
      
      reject('Template not provided.');
    });
  }
  
  function logError(error) {
    busy = false;
    console.error(error);
  }
  
  function escToClose(element) {
    var keyup = function (event) {
      if(event.keyCode === ESCAPE) {
        element.remove();
      }
    };
    
    body.on('keyup', keyup);
    
    element.on('$destroy', function () {
      body.off('keyup', keyup);
    });
  }

  function focusOnOpen(element) {
    $mdUtil.nextTick(function () {
      var autofocus = $mdUtil.findFocusTarget(element);
      
      if(autofocus) {
        autofocus.focus();
      }
    }, false);
  }

  function positionDialog(element, target) {
    var table = angular.element(target).controller('mdCell').getTable();
    
    var getHeight = function () {
      return element.prop('clientHeight');
    };
    
    var getSize = function () {
      return {
        width: getWidth(),
        height: getHeight()
      };
    };
    
    var getTableBounds = function () {
      var parent = table.parent();
      
      if(parent.prop('tagName') === 'MD-TABLE-CONTAINER') {
        return parent[0].getBoundingClientRect();
      } else {
        return table[0].getBoundingClientRect();
      }
    };
    
    var getWidth = function () {
      return element.prop('clientWidth');
    };
    
    var reposition = function () {
      var size = getSize();
      var cellBounds = target.getBoundingClientRect();
      var tableBounds = getTableBounds();
      
      if(size.width > tableBounds.right - cellBounds.left) {
        element.css('left', tableBounds.right - size.width + 'px');
      } else {
        element.css('left', cellBounds.left + 'px');
      }
      
      if(size.height > tableBounds.bottom - cellBounds.top) {
        element.css('top', tableBounds.bottom - size.height + 'px');
      } else {
        element.css('top', cellBounds.top + 1 + 'px');
      }
      
      element.css('minWidth', cellBounds.width + 'px');
    };
    
    var watchWidth = $rootScope.$watch(getWidth, reposition);
    var watchHeight = $rootScope.$watch(getHeight, reposition);
    
    $window.addEventListener('resize', reposition);
    
    element.on('$destroy', function () {
      watchWidth();
      watchHeight();
      
      $window.removeEventListener('resize', reposition);
    });
  }
  
  function preset(size, options) {
    
    function getAttrs() {
      var attrs = 'type="' + (options.type || 'text') + '"';
      
      for(var attr in options.validators) {
        attrs += ' ' + attr + '="' + options.validators[attr] + '"';
      }
      
      return attrs;
    }
    
    return {
      controller: ['$element', '$q', 'save', '$scope', function ($element, $q, save, $scope) {
        function update() {
          if($scope.editDialog.$invalid) {
            return $q.reject();
          }
          
          if(angular.isFunction(save)) {
            return $q.when(save($scope.editDialog.input));
          }
          
          return $q.resolve();
        }
        
        this.dismiss = function () {
          $element.remove();
        };
        
        this.getInput = function () {
          return $scope.editDialog.input;
        };
        
        $scope.dismiss = this.dismiss;
        
        $scope.submit = function () {
          update().then(function () {
            $scope.dismiss();
          });
        };
      }],
      locals: {
        save: options.save
      },
      scope: {
        cancel: options.cancel || 'Cancel',
        messages: options.messages,
        model: options.modelValue,
        ok: options.ok || 'Save',
        placeholder: options.placeholder,
        title: options.title,
        size: size
      },
      template:
        '<md-edit-dialog>' +
          '<div layout="column" class="md-content">' +
            '<div ng-if="size === \'large\'" class="md-title">{{title || \'Edit\'}}</div>' +
            '<form name="editDialog" layout="column" ng-submit="submit(model)">' +
              '<md-input-container md-no-float>' +
                '<input name="input" ng-model="model" md-autofocus placeholder="{{placeholder}} "' + getAttrs() + '>' +
                '<div ng-messages="editDialog.input.$error">' +
                  '<div ng-repeat="(key, message) in messages" ng-message="{{key}}">{{message}}</div>' +
                '</div>' +
              '</md-input-container>' +
            '</form>' +
          '</div>' +
          '<div ng-if="size === \'large\'" layout="row" layout-align="end" class="md-actions">' +
            '<md-button class="md-primary" ng-click="dismiss()">{{cancel}}</md-button>' +
            '<md-button class="md-primary" ng-click="submit()">{{ok}}</md-button>' +
          '</div>' +
        '</md-edit-dialog>'
    };
  }
  
  this.show = function (options) {
    if(busy) {
      return $q.reject();
    }
    
    busy = true;
    options = angular.extend({}, defaultOptions, options);
    
    if(!options.targetEvent) {
      return logError('options.targetEvent is required to align the dialog with the table cell.');
    }
    
    if(!options.targetEvent.currentTarget.classList.contains('md-cell')) {
      return logError('The event target must be a table cell.');
    }
    
    if(options.bindToController && !options.controllerAs) {
      return logError('You must define options.controllerAs when options.bindToController is true.');
    }
    
    options.target = options.targetEvent.currentTarget;
    
    var promise = getTemplate(options);
    var promises = [promise];
    
    for(var prop in options.resolve) {
      promise = options.resolve[prop];
      promises.push($q.when(angular.isFunction(promise) ? promise() : promise));
    }
    
    promise = $q.all(promises);
    
    promise['catch'](logError);
    
    return promise.then(function (results) {
      var template = results.shift();
      
      for(var prop in options.resolve) {
        options.resolve[prop] = results.shift();
      }
      
      return build(template, options);
    });
  };
  
  this.small = function (options) {
    return this.show(angular.extend({}, options, preset('small', options)));
  }.bind(this);
  
  this.large = function (options) {
    return this.show(angular.extend({}, options, preset('large', options)));
  }.bind(this);
  
  return this;
}

mdEditDialog.$inject = ['$compile', '$controller', '$document', '$mdUtil', '$q', '$rootScope', '$templateCache', '$templateRequest', '$window'];


angular.module('md.data.table').directive('mdFoot', mdFoot);

function mdFoot() {

  function compile(tElement) {
    tElement.addClass('md-foot');
  }

  return {
    compile: compile,
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdHead', mdHead);

function mdHead($compile) {

  function compile(tElement) {
    tElement.addClass('md-head');
    return postLink;
  }
  
  // empty controller to be bind scope properties to
  function Controller() {
    
  }
  
  function postLink(scope, element, attrs, tableCtrl) {
    // because scope.$watch is unpredictable
    var oldValue = new Array(2);
    
    function addCheckboxColumn() {
      element.children().prepend('<th class="md-column md-checkbox-column">');
    }
    
    function attatchCheckbox() {
      element.prop('lastElementChild').firstElementChild.appendChild($compile(createCheckBox())(scope)[0]);
    }
    
    function createCheckBox() {
      return angular.element('<md-checkbox>').attr({
        'aria-label': 'Select All',
        'ng-click': 'toggleAll()',
        'ng-checked': 'allSelected()',
        'ng-disabled': '!getSelectableRows().length'
      });
    }
    
    function detachCheckbox() {
      var cell = element.prop('lastElementChild').firstElementChild;
      
      if(cell.classList.contains('md-checkbox-column')) {
        angular.element(cell).empty();
      }
    }
    
    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }
    
    function mdSelectCtrl(row) {
      return angular.element(row).controller('mdSelect');
    }
    
    function removeCheckboxColumn() {
      Array.prototype.some.call(element.find('th'), function (cell) {
        return cell.classList.contains('md-checkbox-column') && cell.remove();
      });
    }
    
    scope.allSelected = function () {
      var rows = scope.getSelectableRows();
      
      return rows.length && rows.every(function (row) {
        return row.isSelected();
      });
    };
    
    scope.getSelectableRows = function () {
      return tableCtrl.getBodyRows().map(mdSelectCtrl).filter(function (ctrl) {
        return ctrl && !ctrl.disabled;
      });
    };
    
    scope.selectAll = function () {
      tableCtrl.getBodyRows().map(mdSelectCtrl).forEach(function (ctrl) {
        if(ctrl && !ctrl.isSelected()) {
          ctrl.select();
        }
      });
    };
    
    scope.toggleAll = function () {
      return scope.allSelected() ? scope.unSelectAll() : scope.selectAll();
    };
    
    scope.unSelectAll = function () {
      tableCtrl.getBodyRows().map(mdSelectCtrl).forEach(function (ctrl) {
        if(ctrl && ctrl.isSelected()) {
          ctrl.deselect();
        }
      });
    };
    
    scope.$watchGroup([enableRowSelection, tableCtrl.enableMultiSelect], function (newValue) {
      if(newValue[0] !== oldValue[0]) {
        if(newValue[0]) {
          addCheckboxColumn();
          
          if(newValue[1]) {
            attatchCheckbox();
          }
        } else {
          removeCheckboxColumn();
        }
      } else if(newValue[0] && newValue[1] !== oldValue[1]) {
        if(newValue[1]) {
          attatchCheckbox();
        } else {
          detachCheckbox();
        }
      }
      
      angular.copy(newValue, oldValue);
    });
  }
  
  return {
    bindToController: true,
    compile: compile,
    controller: Controller,
    controllerAs: '$mdHead',
    require: '^^mdTable',
    restrict: 'A',
    scope: {
      order: '=?mdOrder',
      onReorder: '=?mdOnReorder'
    }
  };
}

mdHead.$inject = ['$compile'];

angular.module('md.data.table').directive('mdRow', mdRow);

function mdRow() {

  function compile(tElement) {
    tElement.addClass('md-row');
    return postLink;
  }
  
  function postLink(scope, element, attrs, tableCtrl) {
    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }
    
    function isBodyRow() {
      return tableCtrl.getBodyRows().indexOf(element[0]) !== -1;
    }
    
    function isChild(node) {
      return element[0].contains(node[0]);
    }
    
    if(isBodyRow()) {
      var cell = angular.element('<td class="md-cell">');
      
      scope.$watch(enableRowSelection, function (enable) {
        // if a row is not selectable, prepend an empty cell to it
        if(enable && !attrs.mdSelect) {
          if(!isChild(cell)) {
            element.prepend(cell);
          }
          return;
        }
        
        if(isChild(cell)) {
          cell.remove();
        }
      });
    }
  }

  return {
    compile: compile,
    require: '^^mdTable',
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdSelect', mdSelect);

function mdSelect($compile, $parse) {

  // empty controller to bind scope properties to
  function Controller() {

  }

  function postLink(scope, element, attrs, ctrls) {
    var self = ctrls.shift();
    var tableCtrl = ctrls.shift();
    var getId = $parse(attrs.mdSelectId);

    self.id = getId(self.model);

    if(tableCtrl.$$rowSelect && self.id) {
      if(tableCtrl.$$hash.has(self.id)) {
        var index = tableCtrl.selected.indexOf(tableCtrl.$$hash.get(self.id));

        // if the item is no longer selected remove it
        if(index === -1) {
          tableCtrl.$$hash.purge(self.id);
        }

        // if the item is not a reference to the current model update the reference
        else if(!tableCtrl.$$hash.equals(self.id, self.model)) {
          tableCtrl.$$hash.update(self.id, self.model);
          tableCtrl.selected.splice(index, 1, self.model);
        }

      } else {

        // check if the item has been selected
        tableCtrl.selected.some(function (item, index) {
          if(getId(item) === self.id) {
            tableCtrl.$$hash.update(self.id, self.model);
            tableCtrl.selected.splice(index, 1, self.model);

            return true;
          }
        });
      }
    }

    self.isSelected = function () {
      if(!tableCtrl.$$rowSelect) {
        return false;
      }

      if(self.id) {
        return tableCtrl.$$hash.has(self.id);
      }

      return tableCtrl.selected.indexOf(self.model) !== -1;
    };

    self.select = function () {
      if(self.disabled) {
        return;
      }

      if(tableCtrl.enableMultiSelect()) {
        tableCtrl.selected.push(self.model);
      } else {
        tableCtrl.selected.splice(0, tableCtrl.selected.length, self.model);
      }

      if(angular.isFunction(self.onSelect)) {
        self.onSelect(self.model);
      }
    };

    self.deselect = function () {
      if(self.disabled) {
        return;
      }

      tableCtrl.selected.splice(tableCtrl.selected.indexOf(self.model), 1);

      if(angular.isFunction(self.onDeselect)) {
        self.onDeselect(self.model);
      }
    };

    self.toggle = function (event) {
      if(event && event.stopPropagation) {
        event.stopPropagation();
      }

      return self.isSelected() ? self.deselect() : self.select();
    };

    function autoSelect() {
      return attrs.mdAutoSelect === '' || self.autoSelect;
    }

    function createCheckbox() {
      var checkbox = angular.element('<md-checkbox>').attr({
        'aria-label': 'Select Row',
        'ng-click': '$mdSelect.toggle($event)',
        'ng-checked': '$mdSelect.isSelected()',
        'ng-disabled': '$mdSelect.disabled'
      });

      return angular.element('<td class="md-cell md-checkbox-cell">').append($compile(checkbox)(scope));
    }

    function disableSelection() {
      Array.prototype.some.call(element.children(), function (child) {
        return child.classList.contains('md-checkbox-cell') && element[0].removeChild(child);
      });

      if(autoSelect()) {
        element.off('click', toggle);
      }
    }

    function enableSelection() {
      element.prepend(createCheckbox());

      if(autoSelect()) {
        element.on('click', toggle);
      }
    }

    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }

    function onSelectChange(selected) {
      if(!self.id) {
        return;
      }

      if(tableCtrl.$$hash.has(self.id)) {
        // check if the item has been deselected
        if(selected.indexOf(tableCtrl.$$hash.get(self.id)) === -1) {
          tableCtrl.$$hash.purge(self.id);
        }

        return;
      }

      // check if the item has been selected
      if(selected.indexOf(self.model) !== -1) {
        tableCtrl.$$hash.update(self.id, self.model);
      }
    }

    function toggle(event) {
      scope.$applyAsync(function () {
        self.toggle(event);
      });
    }

    scope.$watch(enableRowSelection, function (enable) {
      if(enable) {
        enableSelection();
      } else {
        disableSelection();
      }
    });

    scope.$watch(autoSelect, function (newValue, oldValue) {
      if(newValue === oldValue) {
        return;
      }

      if(tableCtrl.$$rowSelect && newValue) {
        element.on('click', toggle);
      } else {
        element.off('click', toggle);
      }
    });

    scope.$watch(self.isSelected, function (isSelected) {
      return isSelected ? element.addClass('md-selected') : element.removeClass('md-selected');
    });

    scope.$watch(tableCtrl.enableMultiSelect, function (multiple) {
      if(tableCtrl.$$rowSelect && !multiple) {
        // remove all but the first selected item
        tableCtrl.selected.splice(1);
      }
    });

    tableCtrl.registerModelChangeListener(onSelectChange);

    element.on('$destroy', function () {
      tableCtrl.removeModelChangeListener(onSelectChange);
    });
  }

  return {
    bindToController: true,
    controller: Controller,
    controllerAs: '$mdSelect',
    link: postLink,
    require: ['mdSelect', '^^mdTable'],
    restrict: 'A',
    scope: {
      model: '=mdSelect',
      disabled: '=ngDisabled',
      onSelect: '=?mdOnSelect',
      onDeselect: '=?mdOnDeselect',
      autoSelect: '=mdAutoSelect'
    }
  };
}

mdSelect.$inject = ['$compile', '$parse'];

angular.module('md.data.table').directive('mdTable', mdTable);

function Hash() {
  var keys = {};
  
  this.equals = function (key, item) {
    return keys[key] === item;
  };

  this.get = function (key) {
    return keys[key];
  };
  
  this.has = function (key) {
    return keys.hasOwnProperty(key);
  };

  this.purge = function (key) {
    delete keys[key];
  };
  
  this.update = function (key, item) {
    keys[key] = item;
  };
}

function mdTable() {
  
  function compile(tElement, tAttrs) {
    tElement.addClass('md-table');
    
    if(tAttrs.hasOwnProperty('mdProgress')) {
      var body = tElement.find('tbody')[0];
      var progress = angular.element('<thead class="md-table-progress">');
      
      if(body) {
        tElement[0].insertBefore(progress[0], body);
      }
    }
  }
  
  function Controller($attrs, $element, $q, $scope) {
    var self = this;
    var queue = [];
    var watchListener;
    var modelChangeListeners = [];
    
    self.$$hash = new Hash();
    self.$$columns = {};
    
    function enableRowSelection() {
      self.$$rowSelect = true;
      
      watchListener = $scope.$watchCollection('$mdTable.selected', function (selected) {
        modelChangeListeners.forEach(function (listener) {
          listener(selected);
        });
      });
      
      $element.addClass('md-row-select');
    }
    
    function disableRowSelection() {
      self.$$rowSelect = false;
      
      if(angular.isFunction(watchListener)) {
        watchListener();
      }
      
      $element.removeClass('md-row-select');
    }
    
    function resolvePromises() {
      if(!queue.length) {
        return $scope.$applyAsync();
      }
      
      queue[0]['finally'](function () {
        queue.shift();
        resolvePromises();
      });
    }
    
    function rowSelect() {
      return $attrs.mdRowSelect === '' || self.rowSelect;
    }
    
    function validateModel() {
      if(!self.selected) {
        return console.error('Row selection: ngModel is not defined.');
      }
      
      if(!angular.isArray(self.selected)) {
        return console.error('Row selection: Expected an array. Recived ' + typeof self.selected + '.');
      }
      
      return true;
    }
    
    self.columnCount = function () {
      return self.getRows($element[0]).reduce(function (count, row) {
        return row.cells.length > count ? row.cells.length : count;
      }, 0);
    };
    
    self.getRows = function (element) {
      return Array.prototype.filter.call(element.rows, function (row) {
        return !row.classList.contains('ng-leave');
      });
    };
    
    self.getBodyRows = function () {
      return Array.prototype.reduce.call($element.prop('tBodies'), function (result, tbody) {
        return result.concat(self.getRows(tbody));
      }, []);
    };
    
    self.getElement = function () {
      return $element;
    };
    
    self.getHeaderRows = function () {
      return self.getRows($element.prop('tHead'));
    };
    
    self.enableMultiSelect = function () {
      return $attrs.multiple === '' || $scope.$eval($attrs.multiple);
    };
    
    self.waitingOnPromise = function () {
      return !!queue.length;
    };
    
    self.queuePromise = function (promise) {
      if(!promise) {
        return;
      }
      
      if(queue.push(angular.isArray(promise) ? $q.all(promise) : $q.when(promise)) === 1) {
        resolvePromises();
      }
    };
    
    self.registerModelChangeListener = function (listener) {
      modelChangeListeners.push(listener);
    };
    
    self.removeModelChangeListener = function (listener) {
      var index = modelChangeListeners.indexOf(listener);
      
      if(index !== -1) {
        modelChangeListeners.splice(index, 1);
      }
    };
    
    if($attrs.hasOwnProperty('mdProgress')) {
      $scope.$watch('$mdTable.progress', self.queuePromise);
    }
    
    $scope.$watch(rowSelect, function (enable) {
      if(enable && !!validateModel()) {
        enableRowSelection();
      } else {
        disableRowSelection();
      }
    });
  }
  
  Controller.$inject = ['$attrs', '$element', '$q', '$scope'];
  
  return {
    bindToController: true,
    compile: compile,
    controller: Controller,
    controllerAs: '$mdTable',
    restrict: 'A',
    scope: {
      progress: '=?mdProgress',
      selected: '=ngModel',
      rowSelect: '=mdRowSelect'
    }
  };
}

angular.module('md.data.table').directive('mdTablePagination', mdTablePagination);

function mdTablePagination() {

  function compile(tElement) {
    tElement.addClass('md-table-pagination');
  }

  function Controller($attrs, $mdUtil, $scope) {
    var self = this;
    var defaultLabel = {
      page: 'Página:',
      rowsPerPage: 'Linhas por página:',
      of: 'de'
    };

    self.label = angular.copy(defaultLabel);

    function isPositive(number) {
      return parseInt(number, 10) > 0;
    }

    self.first = function () {
      self.page = 1;
      self.onPaginationChange();
    };

    self.hasNext = function () {
      return self.page * self.limit < self.total;
    };

    self.hasPrevious = function () {
      return self.page > 1;
    };

    self.last = function () {
      self.page = self.pages();
      self.onPaginationChange();
    };

    self.max = function () {
      return self.hasNext() ? self.page * self.limit : self.total;
    };

    self.min = function () {
      return isPositive(self.total) ? self.page * self.limit - self.limit + 1 : 0;
    };

    self.next = function () {
      self.page++;
      self.onPaginationChange();
    };

    self.onPaginationChange = function () {
      if(angular.isFunction(self.onPaginate)) {
        $mdUtil.nextTick(function () {
          self.onPaginate(self.page, self.limit);
        });
      }
    };

    self.pages = function () {
      return isPositive(self.total) ? Math.ceil(self.total / (isPositive(self.limit) ? self.limit : 1)) : 1;
    };

    self.previous = function () {
      self.page--;
      self.onPaginationChange();
    };

    self.showBoundaryLinks = function () {
      return $attrs.mdBoundaryLinks === '' || self.boundaryLinks;
    };

    self.showPageSelect = function () {
      return $attrs.mdPageSelect === '' || self.pageSelect;
    };

    $scope.$watch('$pagination.limit', function (newValue, oldValue) {
      if(isNaN(newValue) || isNaN(oldValue) || newValue === oldValue) {
        return;
      }

      // find closest page from previous min
      self.page = Math.floor(((self.page * oldValue - oldValue) + newValue) / (isPositive(newValue) ? newValue : 1));
      self.onPaginationChange();
    });

    $attrs.$observe('mdLabel', function (label) {
      angular.extend(self.label, defaultLabel, $scope.$eval(label));
    });

    $scope.$watch('$pagination.total', function (newValue, oldValue) {
      if(isNaN(newValue) || newValue === oldValue) {
        return;
      }

      if(self.page > self.pages()) {
        self.last();
      }
    });
  }

  Controller.$inject = ['$attrs', '$mdUtil', '$scope'];

  return {
    bindToController: {
      boundaryLinks: '=?mdBoundaryLinks',
      disabled: '=ngDisabled',
      limit: '=mdLimit',
      page: '=mdPage',
      pageSelect: '=?mdPageSelect',
      onPaginate: '=?mdOnPaginate',
      limitOptions: '=?mdLimitOptions',
      total: '@mdTotal'
    },
    compile: compile,
    controller: Controller,
    controllerAs: '$pagination',
    restrict: 'E',
    scope: {},
    templateUrl: 'md-table-pagination.html'
  };
}

angular.module('md.data.table').directive('mdTableProgress', mdTableProgress);

function mdTableProgress() {

  function postLink(scope, element, attrs, tableCtrl) {
    scope.columnCount = tableCtrl.columnCount;
    scope.deferred = tableCtrl.waitingOnPromise;
  }

  return {
    link: postLink,
    require: '^^mdTable',
    restrict: 'C',
    scope: {},
    templateUrl: 'md-table-progress.html'
  };
}

angular.module('md.data.table').directive('virtualPageSelect', virtualPageSelect);

function virtualPageSelect() {

  function Controller($element, $scope) {
    var self = this;
    var content = $element.find('md-content');
    
    self.pages = [];
    
    function getMin(pages, total) {
      return Math.min(pages, isFinite(total) && isPositive(total) ? total : 1);
    }
    
    function isPositive(number) {
      return number > 0;
    }
    
    function setPages(max) {
      if(self.pages.length > max) {
        return self.pages.splice(max);
      }
      
      for(var i = self.pages.length; i < max; i++) {
        self.pages.push(i + 1);
      }
    }
    
    content.on('scroll', function () {
      if((content.prop('clientHeight') + content.prop('scrollTop')) >= content.prop('scrollHeight')) {
        $scope.$applyAsync(function () {
          setPages(getMin(self.pages.length + 10, self.total));
        });
      }
    });
    
    $scope.$watch('$pageSelect.total', function (total) {
      setPages(getMin(Math.max(self.pages.length, 10), total));
    });
  }

  Controller.$inject = ['$element', '$scope'];

  return {
    bindToController: {
      total: '@'
    },
    controller: Controller,
    controllerAs: '$pageSelect'
  };
}

})(window, angular);
(function () {
    angular.module("su-cadastro-imovel").filter("numeroBr", function(){
  return function(number){
    if(number) {
      var decimal = number.toString().substring(number.toString().lastIndexOf("."));
      var resto = number.toString().substring(0, number.toString().lastIndexOf("."));
      decimal = decimal.replace(".", ".");
      resto = resto.replace(/,/g, ",");
      number = resto + decimal;

      return number;
    }
  };
});
})();


(function () {
    angular.module("su-cadastro-imovel").filter("converterHa", function(){
        return function(number){
            if(number) {
                var decimal = number.toString().substring(number.toString().lastIndexOf("."));
                var resto = number.toString().substring(0, number.toString().lastIndexOf("."));
                decimal = decimal.replace(",", ".");
                resto = resto.replace(/,/g, ",");
                number = resto + decimal;

                return number;
            }
        };
    });
})();

(function () {
  'use strict';
  angular.module('su-cadastro-imovel').filter('filtro', function () {
    return function (data, attr, text) {
      if (!text || text === '') return data;
      var textFiltro = text.toLowerCase();
      return (data ? data : []).filter(function (item) {
        return (
          (typeof item[attr] === 'undefined' ? '' : item[attr])
            .toLowerCase()
            .indexOf(textFiltro) > -1
        );
      });
    };
  });
})();

(function () {
  'use strict';
  angular.module('su-cadastro-imovel').filter('orderByBr2', function () {
    return function (data, campo1, campo2) {
      if (!data) {
        return data;
      }
      data.sort(function (a, b) {
        var palavra1 = a[campo1].concat(a[campo2]);
        var palavra2 = b[campo1].concat(b[campo2]);
        return palavra1.localeCompare(palavra2, 'standard', {
          sensitivity: 'base',
        });
      });
      return data;
    };
  });
})();

(function () {
  'use strict';
  angular.module('su-cadastro-imovel').filter('orderByBr', function () {
    return function (data, campo) {
      if (!data) {
        return data;
      }
      data.sort(function (a, b) {
        return a[campo].localeCompare(b[campo], 'standard', {
          sensitivity: 'base',
        });
      });
      return data;
    };
  });
})();

(function () {
    "use strict";
angular.module("su-cadastro-imovel").directive("cpfCnpjMask", cpfCnpjMask);

function cpfCnpjMask() {

    return {
        restrict: 'A',
        require: 'ngModel',
        link : function(scope, element, attrs, ctrl) {
            var clearValue, format, validations;

                clearValue = function(rawValue) {
                    return rawValue.replace(/[^A-Z0-9]/g, '').slice(0, 14);
                };

                format = function(cleanValue) {

                    var formatedValue;

                    if (cleanValue.length > 11) {                        
                        formatedValue =  cleanValue.replace(/([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})/g,"\$1.\$2.\$3\/\$4\-\$5");
                        return formatedValue.trim().replace(/[^A-Z0-9]$/, '');
                    } else {
                        formatedValue = cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g,"\$1.\$2.\$3\-\$4");
                        return formatedValue.trim().replace(/[^0-9]$/, '');
                    }

                };

                validations = {
                    cpf: function(value) {
                        return validaCpf(value);

                    },
                    cnpj: function(value) {
                        return validaCnpj(value);
                    }
                };

                ctrl.$formatters.push(function formatter(value) {
                    if (ctrl.$isEmpty(value)) {
                        return value;
                    }

                    var cleanValue = clearValue(value);
                    return format(cleanValue);
                });

                ctrl.$parsers.push(function parser(value) {
                    if (ctrl.$isEmpty(value)) {
                        return value;
                    }

                    var cleanValue = clearValue(value);
                    var formattedValue = format(cleanValue);

                    if (ctrl.$viewValue !== formattedValue) {
                        ctrl.$setViewValue(formattedValue);
                        ctrl.$render();
                    }
                    //
                    // if (cleanValue.length > 11) {
                    //     ctrl.$validators['cnpj'] = function () {
                    //       return ctrl.$isEmpty(cleanValue) || validations.cnpj(cleanValue);
                    //     };
                    // } else {
                    //     ctrl.$validators['cpf'] = function () {
                    //         return ctrl.$isEmpty(cleanValue) || validations.cpf(cleanValue);
                    //     };
                    // }

                    return cleanValue;

                });

            

            function calcDigitosPosicoes( digitos, posicoes, somaDigitos ) {

                posicoes = 10;
                somaDigitos = 0;
                digitos = digitos.toString();

                for ( var i = 0; i < digitos.length; i++  ) {

                    somaDigitos = somaDigitos + ( digitos[i] * posicoes );
                    posicoes--;
                    if ( posicoes < 2 ) {
                        posicoes = 9;
                    }
                }

                somaDigitos = somaDigitos % 11;
                if ( somaDigitos < 2 ) {
                    somaDigitos = 0;
                } else {
                    somaDigitos = 11 - somaDigitos;
                }

                var cpf = digitos + somaDigitos;

                return cpf;

            };

            function validaCpf(cpf)
            {
                var numeros, digitos, soma, i, resultado, digitosIguais;
                digitosIguais = 1;
                if (cpf.length < 11)
                    return false;
                for (i = 0; i < cpf.length - 1; i++)
                    if (cpf.charAt(i) != cpf.charAt(i + 1))
                    {
                        digitosIguais = 0;
                        break;
                    }
                if (!digitosIguais)
                {
                    numeros = cpf.substring(0,9);
                    digitos = cpf.substring(9);
                    soma = 0;
                    for (i = 10; i > 1; i--)
                        soma += numeros.charAt(10 - i) * i;
                    resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
                    if (resultado != digitos.charAt(0))
                        return false;
                    numeros = cpf.substring(0,10);
                    soma = 0;
                    for (i = 11; i > 1; i--)
                        soma += numeros.charAt(11 - i) * i;
                    resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
                    if (resultado != digitos.charAt(1))
                        return false;
                    return true;
                }
                else
                    return false;
            };

            function removeMascaraCNPJ(cnpj) {
                const regexCaracteresMascara = /[./-]/g;
                return cnpj.replace(this.regexCaracteresMascara, "");
            }

            function validaCnpj(cnpj) {    
                
                const tamanhoCNPJSemDV = 12;
                const regexCNPJSemDV = /^([A-Z\d]){12}$/;
                const regexCNPJ = /^([A-Z\d]){12}(\d){2}$/;                
                const regexCaracteresNaoPermitidos = /[^A-Z\d./-]/i;
                const valorBase = "0".charCodeAt(0);
                const pesosDV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
                const cnpjZerado = "00000000000000";


                if (!this.regexCaracteresNaoPermitidos.test(cnpj)) {
                    var cnpjSemMascara = this.removeMascaraCNPJ(cnpj);
                    if (this.regexCNPJSemDV.test(cnpjSemMascara) && cnpjSemMascara !== this.cnpjZerado.substring(0, this.tamanhoCNPJSemDV)) {
                        var somatorioDV1 = 0;
                        var somatorioDV2 = 0;
                        for (var i = 0; i < this.tamanhoCNPJSemDV; i++) {
                            const asciiDigito = cnpjSemMascara.charCodeAt(i) - this.valorBase;
                            somatorioDV1 += asciiDigito * this.pesosDV[i + 1];
                            somatorioDV2 += asciiDigito * this.pesosDV[i];
                        }
                        const dv1 = somatorioDV1 % 11 < 2 ? 0 : 11 - (somatorioDV1 % 11);
                        somatorioDV2 += dv1 * this.pesosDV[this.tamanhoCNPJSemDV];
                        const dv2 = somatorioDV2 % 11 < 2 ? 0 : 11 - (somatorioDV2 % 11);
                        const cnpjCalculado = cnpjSemMascara.substring(0, this.tamanhoCNPJSemDV) + dv1 + dv2;
                        return (cnpjCalculado === cnpjSemMascara);
                    }
                }
                return false;
            }

        }
    };

}

}());

(function () {
  'use strict';
  /*global angular*/
  angular.module('ciMapaDirective')
    .constant('MAPA_CONSTANTS_INCORPORACAO', {
      NOTIFICADORES: {
        CENTRALIZAR_POR_MUNICIPIO: 'centralizarPorMunicipio',
        ALTERAR_VISIBILIDADE_BING: 'alterarVisibilidadeBing',
        PONTO_MARCADO: 'pontoMarcado',
        FEATURE_EXCLUIDA: 'featureExcluida',
        TERRENO_DESENHADO: 'terrenoDesenhado',
        UPLOAD_SHAPEFILE: 'uploadShapefileCiMapaDirective',
        UPLOAD_SHAPEFILE_BENFEITORIA: 'uploadShapefileBenfeitoriaCiMapaDirective',
        UPLOAD_SHAPEFILE_UNIDADE_AUTONOMA: 'uploadShapefileUnidadeAutonomaCiMapaDirective',
        VERIFICAR_PONTO_NA_ARESTA: 'verificarPontoNaAresta',
        SET_ID_BENFEITORIA: 'setIdBenfeitoriaCiMapaDirective',
        EXCLUI_BENFEITORIA_BY_ID: 'excluiBenfeitoriaByIdCiMapaDirective',
        DESTACAR_BENFEITORIA: 'destacarBenfeitoriaCiMapaDirective',
        BENFEITORIA_EXCLUIDA: 'benfeitoriaExcluidaCiMapaDirective',
        DESFAZER_ALTERACAO_BENFEITORIA: 'desfazerAlteracaoBenfeitoriaCiMapaDirective',
        LIMPAR_DESENHO: 'limparDesenhoCiMapaDirective',
        RESULTADOPESQUISARIMOVEL: 'resultadoPesquisarImovelCiMapaDirective',
        DIRECIONAR_DETALHAR: 'direcionarDetalharCiMapaDirective',
        ADICIONAR_FEATURE_PONTO: 'adicionarFeaturePontoCiMapaDirective',
        ADICIONAR_FEATURE_PONTO_TESTADA: 'adicionarFeaturePontoTestadaCiMapaDirective',
        ADICIONAR_FEATURE_TERRENO: 'adicionarFeatureTerrenoCiMapaDirective',
        ADICIONAR_FEATURE_BENFEITORIA: 'adicionarFeatureBenfeitoriaCiMapaDirective',
        ADICIONAR_FEATURE_UNIDADE_AUTONOMA: 'adicionarFeatureUnidadeAutonomaCiMapaDirective',
        REMOVER_FEATURE_BY_ID: 'removerFeatureByIdCiMapaDirective',
        ATUALIZAR_UF:'setUfbyPonto',
        PREENCHER_AREA_PROJECAO: 'preencherAreaProjecaoCiMapaDirective',
        SET_VETORIZAR_EM_TELA: 'setVetorizarEmTelaCiMapaDirective',
        DELETESHAPEFILECARTORIAL: 'deleteShapeFileCartorialCiMapaDirective',
        DELETESHAPEFILECADASTRAL: 'deleteShapeFileCadastralCiMapaDirective',
        LIMPAR_TIPO_IMOVEL: 'limparTipoImovel',
        REMOVER_PONTO_EDIFICACOES_TERRENO_UA: 'removerPontoEdificacoesTerrrenoUnidadeAutonomaCiMapaDirective',
        PONTO_IN_UNIDADE_AUTONOMA: 'pontoInUnidadeAutonoma',
        FEATURE_EXCLUIDA_UNIDADE_AUTONOMA: 'featureExcluidaUnidadeAutonomaCiMapaDirective',
        MARCAR_PONTO_POR_COORDENADAS: 'marcarPontoPorCoordenadasCiMapaDirective',
        ATUALIZAR_COORDENADAS_DO_PONTO: 'atualizarCoordenadasDoPontoCiMapaDirective',
        ATUALIZAR_COORDENADAS_DO_PONTO_TESTADA: 'atualizarCoordenadasDoPontoTestadaCiMapaDirective',
        REMOVER_EDIFICACOES: 'removerEdificacoesCiMapaDirective',
        GEOMETRIA_BENFEITORIA_ALTERADA: 'geometriaBenfeitoriaAlteradaCiMapaDirective',
        GEOMETRIA_UNIDADE_AUTONOMA_ALTERADA: 'geometriaUnidadeAutonomaAlteradaCiMapaDirective',
        GEOMETRIA_TERRENO_CADASTRAL_ALTERADA: 'geometriaTerrenoCadastralAlteradaCiMapaDirective',
        LIMPAR_ABA: 'limparAba',
        CARREGAR_UTILIZACOES_BENFEITORIAS: 'carregarUtilizacoesBenfeitorias'
      },
      FEATURE_ID: {
        PONTO_IMOVEL: 'pontoImovel',
        PONTO_IMOVEL_ORIGINAL: 'pontoImovelOriginal',
        PONTO_IMOVEL_TESTADA: 'pontoImovelTestada',
        TERRENO_CADASTRAL_ALTERADO: 'terrenoCadastralAlterado',
        TERRENO_CADASTRAL: 'terrenoCadastral',
        TERRENO_CADASTRAL_SOBREPOSTO: 'terrenoCadastralSobreposto',
        TERRENO_CARTORIAL_SOBREPOSTO: 'terrenoCartorialSobreposto',
        TERRENO_BENFEITORIA_SOBREPOSTO: 'terrenoBenfeitoriaSobreposto',
        UNIDADE_AUTONOMA_SOBREPOSTA: 'unidadeAutonomaSobreposta',
        CENTRO_SEGMENTO: 'centroSegmento',
        TERRENO_CARTORIAL: 'terrenoCartorial',
        TERRENO_CARTORIAL_ALTERADO: 'terrenoCartorialAlterado',
        ACESSAO_BENFEITORIA: 'acessaoBenfeitoria',
        UNIDADE_AUTONOMA: 'unidadeAutonoma',
        UNIDADE_AUTONOMA_ALTERADO: 'unidadeAutonomaAlterado'
      },
      GEOMETRY: {
        GEOMETRIA_INICIAL: [-55.41, -15.82],
        CENTRALIZAR_IMOVEL: [1246, 500]
      },
      TIPO_IMOVEL: {
        UNIDADE_AUTONOMA: 'UNIDADE AUTÔNOMA'
      },
      LAYERS: {
        PONTO_UNIDADE_AUTONOMA: 'pontoUnidadeAutonoma'
      }
    });
}());

( function () {
    "use strict";
    directive.$inject = ["escopoCompartilhadoService"];
    angular.module("su-cadastro-imovel").directive("campoAjuda", directive);

    function directive(escopoCompartilhadoService) {
        return {
            restrict: 'A',
            link: function (scope, elm, attrs, ctrl) {
                var codigoSistemico = attrs.campoAjuda;
                elm.attr('title', retornaAjuda());

                function retornaAjuda () {
                    var hints = escopoCompartilhadoService.getObjeto('hints');
                    if (hints) {
                        for (var i = 0; i < hints.length; i++) {
                            if (hints[i].codigoSistemico === codigoSistemico) {
                                return hints[i].ajuda;
                            }
                        }
                    }
                }
            }
        };
    }
})();

(function () {
    'use strict';
    /*global angular*/
    angular.module('su-cadastro-imovel')
        .constant('HINTS_CONTANTS', {
            CODIGOS_SISTEMICO_FORM: {
                INC_LOCALIZACAO_IMOVEL: 'INC_FORM_LOC',
                INC_FORM_DADOS_GERAIS: 'INC_FORM_DADOS_GERAIS',
                INC_FORM_TERRENO: 'INC_FORM_TERRENO',
                INC_FORM_PROPRIETARIO: 'INC_FORM_PROPRIETARIO',
                INC_FORM_TITULO_AQUIS: 'INC_FORM_TITULO_AQUIS',
                INC_FORM_UPLOAD_ARQUIVOS: 'INC_FORM_UPLOAD_ARQUIVOS',
                INC_FORM_UPLOAD_FOTOS: 'INC_FORM_UPLOAD_FOTOS',
                INC_FORM_UPLOAD_ARQ_TERRENO: 'INC_FORM_UPLOAD_ARQ_TERRENO',
                INC_FORM_TERCEIROS: 'INC_FORM_TERCEIROS',
                INC_FORM_DADOS_MEDIDOR: 'INC_FORM_DADOS_MEDIDOR',
                INC_FORM_DOCUMENTACAO: 'INC_FORM_DOCUMENTACAO',
                INC_FORM_BENFEITORIA: 'INC_FORM_BENFEITORIA',
                INC_FORM_UN_AUTONOMA: 'INC_FORM_UN_AUTONOMA',
                INC_FORM_CANCEL_IMOVEL: 'INC_FORM_CANCEL_IMOVEL',
                INC_FORM_HOM_IMOVEL: 'INC_FORM_HOM_IMOVEL',
                INC_FORM_CONSULTA_IMOVEL: 'INC_FORM_CONSULTA_IMOVEL',
                INC_FORM_PESQ_IMOVEL: 'INC_FORM_PESQ_IMOVEL',
                INC_FORM_CONSULTA_SUSPENSAO: 'INC_FORM_CONSULTA_SUSPENSAO',
                INC_FORM_CANCEL_SUSPENSAO: 'INC_FORM_CANCEL_SUSPENSAO',
                INC_FORM_CADAST_SUSPENSAO: 'INC_FORM_CADAST_SUSPENSAO'
            }
        });
}());

(function(){
    "use strict";
    angular.module("su-cadastro-imovel").controller("VisualizaFotoModalCtrl",
        [ '$scope', '$mdDialog', 'slides',

            function VisualizaFotoModalCtrl($scope, $mdDialog, slides){

                var vm = $scope;
                vm.slides = slides;

                vm.fechar = function () {
                    $mdDialog.cancel();
                };
            }])

})();

(function () {
    "use strict";
    directive.$inject = ["$mdDialog"];
    angular.module("su-cadastro-imovel").directive("albumFotos", directive);

    function directive($mdDialog){
        return {
            templateUrl: "scripts-cadastro-imovel/app/directives/album-fotos/template/album-fotos.html",
            scope: {
                model: "="
            },
            controller: function () {
                var vm = this;
                vm.slides = [];
            },
            controllerAs: "ctrl",
            link: function($scope, el, attrs, ctrl) {

                var vm = $scope;

                $scope.$watchCollection('model', function(model, oldModel) {
                    ctrl.slides = [];
                    var i = 1, imagem;
                    angular.forEach(model, function (foto) {
                        imagem = angular.copy(foto);
                        imagem.imagem.id = i;
                        ctrl.slides.push(imagem.imagem);
                        i++;
                    });
                });


                vm.visualizaFotosModal = function(ev) {


                    $mdDialog.show({
                        controller: 'VisualizaFotoModalCtrl',
                        controllerAs: 'visualizaFotoModalCtrl',
                        templateUrl: 'scripts-cadastro-imovel/app/directives/album-fotos/template/album-fotos-detalhar-modal.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose:true,
                        locals: {
                            slides : ctrl.slides
                        }
                    });
                };
            }
        }
    }
})();

(function () {
angular.module('su-cadastro-imovel')
.directive('limittext', function() {
  return {
    restrict:'EA',
    scope:{
      texto:'='
    },
    replace:false,
    controller:["$scope", function($scope){

      $scope.$watch('texto', function(newValue, oldValue) {
        if($scope.texto!=undefined){
          $scope.show=$scope.texto.length < 100;
        }else{
          $scope.show=true;
        }
      });

    }],
    templateUrl: 'scripts-cadastro-imovel/app/components/limitText/limitText.html'
  };
});
})();

(function () {
'use strict';

angular.module('su-cadastro-imovel')
    .factory('successInterceptor', ["$mdToast", function ($mdToast) {
        return {
            'responseSucces': function (response) {
                if (response.status == 201) {
                    $mdToast.simple().content("Inclusão realizada com sucesso").theme("success-toast")
                }
            }
        };
    }]);
})();

(function () {
    angular.module('su-cadastro-imovel')
        .directive('incCarousel', function() {
            return {
                restrict:'EA',
                scope:{
                    imagens:'=',
                    src:'=',
                    linkImagem:'&',
                    startIndex: '=?'
                },
                replace:false,
                controller:["$scope", "$timeout", function($scope,$timeout){
                    $scope.selectedIndex = $scope.startIndex && $scope.startIndex >= 0 ? $scope.startIndex : 0;
                    $scope.transition='';
                    $scope.transitionTime=500;
                    if($scope.src==true){
                        $scope.$watch('imagens', function(newValue, oldValue) {
                            angular.forEach($scope.imagens, function(value, index){
                                angular.forEach(value, function(key, index2){
                                    if(key.length && key.length > 1000 && value.src==undefined){
                                        var image = new Image();
                                        image.onerror = function() {

                                        };
                                        image.onload = function() {
                                            value.src='data:image/jpeg;base64,'+key;
                                        };
                                        image.src = 'data:image/jpeg;base64,'+key;
                                    }
                                });
                            });
                        }, true);
                    }
                    $scope.avanca=function(){
                        if($scope.selectedIndex <=$scope.imagens.length){
                            $scope.transition='right';
                            $timeout(function(){
                                $scope.transition='left';
                                $scope.selectedIndex++;
                                $timeout(function(){
                                    $scope.transition='';
                                },$scope.transitionTime);
                            },$scope.transitionTime);
                        }
                    };

                    $scope.retorna=function(){
                        if($scope.selectedIndex >0){
                            $scope.transition='left';
                            $timeout(function(){
                                $scope.transition='right';
                                $scope.selectedIndex--;
                                $timeout(function(){
                                    $scope.transition='';
                                },$scope.transitionTime);
                            },$scope.transitionTime);
                        }
                    };

                    $scope.acessaImagem = function (ev) {

                        if(angular.isFunction($scope.linkImagem)) {

                            $scope.linkImagem({ev: ev});

                        }

                    }

                }],
                templateUrl: 'scripts-cadastro-imovel/app/components/carousel/carousel.html'
            };
        });

})();

(function () {
    "use strict";
    modalFotosController.$inject = ["tituloModal", "fotos", "$mdDialog"];
    angular.module("su-cadastro-imovel").controller("ModalFotosController", modalFotosController);

    function modalFotosController(tituloModal, fotos, $mdDialog) {

        var vm = this;

        vm.tituloModal = tituloModal;
        vm.fotos = fotos;

        vm.fechar = function () {
            $mdDialog.cancel();
        }
    }
})();

(function(f){if(typeof exports==="object"&&typeof module!=="undefined"){module.exports=f()}else if(typeof define==="function"&&define.amd){define([],f)}else{var g;if(typeof window!=="undefined"){g=window}else if(typeof global!=="undefined"){g=global}else if(typeof self!=="undefined"){g=self}else{g=this}g.turf = f()}})(function(){var define,module,exports;return (function e(t,n,r){function s(o,u){if(!n[o]){if(!t[o]){var a=typeof require=="function"&&require;if(!u&&a)return a(o,!0);if(i)return i(o,!0);var f=new Error("Cannot find module '"+o+"'");throw f.code="MODULE_NOT_FOUND",f}var l=n[o]={exports:{}};t[o][0].call(l.exports,function(e){var n=t[o][1][e];return s(n?n:e)},l,l.exports,e,t,n,r)}return n[o].exports}var i=typeof require=="function"&&require;for(var o=0;o<r.length;o++)s(r[o]);return s})({1:[function(require,module,exports){
var measureDistance = require('turf-distance');
var point = require('turf-helpers').point;
var bearing = require('turf-bearing');
var destination = require('turf-destination');

/**
 * Takes a {@link LineString|line} and returns a {@link Point|point} at a specified distance along the line.
 *
 * @name along
 * @param {Feature<LineString>} line input line
 * @param {number} distance distance along the line
 * @param {String} [units=miles] can be degrees, radians, miles, or kilometers
 * @return {Feature<Point>} Point `distance` `units` along the line
 * @example
 * var line = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "LineString",
 *     "coordinates": [
 *       [-77.031669, 38.878605],
 *       [-77.029609, 38.881946],
 *       [-77.020339, 38.884084],
 *       [-77.025661, 38.885821],
 *       [-77.021884, 38.889563],
 *       [-77.019824, 38.892368]
 *     ]
 *   }
 * };
 *
 * var along = turf.along(line, 1, 'miles');
 *
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": [line, along]
 * };
 *
 * //=result
 */
module.exports = function (line, distance, units) {
    var coords;
    if (line.type === 'Feature') coords = line.geometry.coordinates;
    else if (line.type === 'LineString') coords = line.coordinates;
    else throw new Error('input must be a LineString Feature or Geometry');

    var travelled = 0;
    for (var i = 0; i < coords.length; i++) {
        if (distance >= travelled && i === coords.length - 1) break;
        else if (travelled >= distance) {
            var overshot = distance - travelled;
            if (!overshot) return point(coords[i]);
            else {
                var direction = bearing(coords[i], coords[i - 1]) - 180;
                var interpolated = destination(coords[i], overshot, direction, units);
                return interpolated;
            }
        } else {
            travelled += measureDistance(coords[i], coords[i + 1], units);
        }
    }
    return point(coords[coords.length - 1]);
};

},{"turf-bearing":2,"turf-destination":3,"turf-distance":4,"turf-helpers":5}],2:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-bearing");

},{"/Users/tmcw/src/turf/packages/turf-bearing":13}],3:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-destination");

},{"/Users/tmcw/src/turf/packages/turf-destination":55}],4:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-distance");

},{"/Users/tmcw/src/turf/packages/turf-distance":60}],5:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-helpers");

},{"/Users/tmcw/src/turf/packages/turf-helpers":71}],6:[function(require,module,exports){
var geometryArea = require('geojson-area').geometry;

/**
 * Takes a one or more features and returns their area
 * in square meters.
 *
 * @param {(Feature|FeatureCollection)} input input features
 * @return {Number} area in square meters
 * @example
 * var polygons = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Polygon",
 *         "coordinates": [[
 *           [-67.031021, 10.458102],
 *           [-67.031021, 10.53372],
 *           [-66.929397, 10.53372],
 *           [-66.929397, 10.458102],
 *           [-67.031021, 10.458102]
 *         ]]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Polygon",
 *         "coordinates": [[
 *           [-66.919784, 10.397325],
 *           [-66.919784, 10.513467],
 *           [-66.805114, 10.513467],
 *           [-66.805114, 10.397325],
 *           [-66.919784, 10.397325]
 *         ]]
 *       }
 *     }
 *   ]
 * };
 *
 * var area = turf.area(polygons);
 *
 * //=area
 */
function area(input) {
    if (input.type === 'FeatureCollection') {
        for (var i = 0, sum = 0; i < input.features.length; i++) {
            if (input.features[i].geometry) {
                sum += geometryArea(input.features[i].geometry);
            }
        }
        return sum;
    } else if (input.type === 'Feature') {
        return geometryArea(input.geometry);
    } else {
        return geometryArea(input);
    }
}
module.exports = area;

},{"geojson-area":7}],7:[function(require,module,exports){
var wgs84 = require('wgs84');

module.exports.geometry = geometry;
module.exports.ring = ringArea;

function geometry(_) {
    var area = 0, i;
    switch (_.type) {
        case 'Polygon':
            return polygonArea(_.coordinates);
        case 'MultiPolygon':
            for (i = 0; i < _.coordinates.length; i++) {
                area += polygonArea(_.coordinates[i]);
            }
            return area;
        case 'Point':
        case 'MultiPoint':
        case 'LineString':
        case 'MultiLineString':
            return 0;
        case 'GeometryCollection':
            for (i = 0; i < _.geometries.length; i++) {
                area += geometry(_.geometries[i]);
            }
            return area;
    }
}

function polygonArea(coords) {
    var area = 0;
    if (coords && coords.length > 0) {
        area += Math.abs(ringArea(coords[0]));
        for (var i = 1; i < coords.length; i++) {
            area -= Math.abs(ringArea(coords[i]));
        }
    }
    return area;
}

/**
 * Calculate the approximate area of the polygon were it projected onto
 *     the earth.  Note that this area will be positive if ring is oriented
 *     clockwise, otherwise it will be negative.
 *
 * Reference:
 * Robert. G. Chamberlain and William H. Duquette, "Some Algorithms for
 *     Polygons on a Sphere", JPL Publication 07-03, Jet Propulsion
 *     Laboratory, Pasadena, CA, June 2007 http://trs-new.jpl.nasa.gov/dspace/handle/2014/40409
 *
 * Returns:
 * {float} The approximate signed geodesic area of the polygon in square
 *     meters.
 */

function ringArea(coords) {
    var p1, p2, p3, lowerIndex, middleIndex, upperIndex,
    area = 0,
    coordsLength = coords.length;

    if (coordsLength > 2) {
        for (i = 0; i < coordsLength; i++) {
            if (i === coordsLength - 2) {// i = N-2
                lowerIndex = coordsLength - 2;
                middleIndex = coordsLength -1;
                upperIndex = 0;
            } else if (i === coordsLength - 1) {// i = N-1
                lowerIndex = coordsLength - 1;
                middleIndex = 0;
                upperIndex = 1;
            } else { // i = 0 to N-3
                lowerIndex = i;
                middleIndex = i+1;
                upperIndex = i+2;
            }
            p1 = coords[lowerIndex];
            p2 = coords[middleIndex];
            p3 = coords[upperIndex];
            area += ( rad(p3[0]) - rad(p1[0]) ) * Math.sin( rad(p2[1]));
        }

        area = area * wgs84.RADIUS * wgs84.RADIUS / 2;
    }

    return area;
}

function rad(_) {
    return _ * Math.PI / 180;
}
},{"wgs84":8}],8:[function(require,module,exports){
module.exports.RADIUS = 6378137;
module.exports.FLATTENING = 1/298.257223563;
module.exports.POLAR_RADIUS = 6356752.3142;

},{}],9:[function(require,module,exports){
var polygon = require('turf-helpers').polygon;

/**
 * Takes a bbox and returns an equivalent {@link Polygon|polygon}.
 *
 * @name bboxPolygon
 * @param {Array<number>} bbox an Array of bounding box coordinates in the form: ```[xLow, yLow, xHigh, yHigh]```
 * @return {Feature<Polygon>} a Polygon representation of the bounding box
 * @example
 * var bbox = [0, 0, 10, 10];
 *
 * var poly = turf.bboxPolygon(bbox);
 *
 * //=poly
 */

module.exports = function (bbox) {
    var lowLeft = [bbox[0], bbox[1]];
    var topLeft = [bbox[0], bbox[3]];
    var topRight = [bbox[2], bbox[3]];
    var lowRight = [bbox[2], bbox[1]];

    return polygon([[
        lowLeft,
        lowRight,
        topRight,
        topLeft,
        lowLeft
    ]]);
};

},{"turf-helpers":10}],10:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],11:[function(require,module,exports){
var each = require('turf-meta').coordEach;

/**
 * Takes a set of features, calculates the bbox of all input features, and returns a bounding box.
 *
 * @name bbox
 * @param {(Feature|FeatureCollection)} geojson input features
 * @return {Array<number>} the bounding box of `input` given
 * as an array in WSEN order (west, south, east, north)
 * @example
 * var input = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [114.175329, 22.2524]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [114.170007, 22.267969]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [114.200649, 22.274641]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [114.186744, 22.265745]
 *       }
 *     }
 *   ]
 * };
 *
 * var bbox = turf.bbox(input);
 *
 * var bboxPolygon = turf.bboxPolygon(bbox);
 *
 * var resultFeatures = input.features.concat(bboxPolygon);
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": resultFeatures
 * };
 *
 * //=result
 */
module.exports = function (geojson) {
    var bbox = [Infinity, Infinity, -Infinity, -Infinity];
    each(geojson, function (coord) {
        if (bbox[0] > coord[0]) bbox[0] = coord[0];
        if (bbox[1] > coord[1]) bbox[1] = coord[1];
        if (bbox[2] < coord[0]) bbox[2] = coord[0];
        if (bbox[3] < coord[1]) bbox[3] = coord[1];
    });
    return bbox;
};

},{"turf-meta":12}],12:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-meta");

},{"/Users/tmcw/src/turf/packages/turf-meta":98}],13:[function(require,module,exports){
var getCoord = require('turf-invariant').getCoord;
//http://en.wikipedia.org/wiki/Haversine_formula
//http://www.movable-type.co.uk/scripts-cadastro-imovel/latlong.html

/**
 * Takes two {@link Point|points} and finds the geographic bearing between them.
 *
 * @name bearing
 * @param {Feature<Point>} start starting Point
 * @param {Feature<Point>} end ending Point
 * @returns {Number} bearing in decimal degrees
 * @example
 * var point1 = {
 *   "type": "Feature",
 *   "properties": {
 *     "marker-color": '#f00'
 *   },
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-75.343, 39.984]
 *   }
 * };
 * var point2 = {
 *   "type": "Feature",
 *   "properties": {
 *     "marker-color": '#0f0'
 *   },
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-75.534, 39.123]
 *   }
 * };
 *
 * var points = {
 *   "type": "FeatureCollection",
 *   "features": [point1, point2]
 * };
 *
 * //=points
 *
 * var bearing = turf.bearing(point1, point2);
 *
 * //=bearing
 */
module.exports = function (start, end) {
    var degrees2radians = Math.PI / 180;
    var radians2degrees = 180 / Math.PI;
    var coordinates1 = getCoord(start);
    var coordinates2 = getCoord(end);

    var lon1 = degrees2radians * coordinates1[0];
    var lon2 = degrees2radians * coordinates2[0];
    var lat1 = degrees2radians * coordinates1[1];
    var lat2 = degrees2radians * coordinates2[1];
    var a = Math.sin(lon2 - lon1) * Math.cos(lat2);
    var b = Math.cos(lat1) * Math.sin(lat2) -
        Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);

    var bearing = radians2degrees * Math.atan2(a, b);

    return bearing;
};

},{"turf-invariant":14}],14:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-invariant");

},{"/Users/tmcw/src/turf/packages/turf-invariant":79}],15:[function(require,module,exports){
var linestring = require('turf-helpers').lineString;
var Spline = require('./spline.js');

/**
 * Takes a {@link LineString|line} and returns a curved version
 * by applying a [Bezier spline](http://en.wikipedia.org/wiki/B%C3%A9zier_spline)
 * algorithm.
 *
 * The bezier spline implementation is by [Leszek Rybicki](http://leszek.rybicki.cc/).
 *
 * @name bezier
 * @param {Feature<LineString>} line input LineString
 * @param {Number} [resolution=10000] time in milliseconds between points
 * @param {Number} [sharpness=0.85] a measure of how curvy the path should be between splines
 * @returns {Feature<LineString>} curved line
 * @example
 * var line = {
 *   "type": "Feature",
 *   "properties": {
 *     "stroke": "#f00"
 *   },
 *   "geometry": {
 *     "type": "LineString",
 *     "coordinates": [
 *       [-76.091308, 18.427501],
 *       [-76.695556, 18.729501],
 *       [-76.552734, 19.40443],
 *       [-74.61914, 19.134789],
 *       [-73.652343, 20.07657],
 *       [-73.157958, 20.210656]
 *     ]
 *   }
 * };
 *
 * var curved = turf.bezier(line);
 * curved.properties = { stroke: '#0f0' };
 *
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": [line, curved]
 * };
 *
 * //=result
 */
module.exports = function (line, resolution, sharpness) {
    var lineOut = linestring([]);

    lineOut.properties = line.properties;

    var spline = new Spline({
        points: line.geometry.coordinates.map(function (pt) {
            return {x: pt[0], y: pt[1]};
        }),
        duration: resolution,
        sharpness: sharpness
    });

    for (var i = 0; i < spline.duration; i += 10) {
        var pos = spline.pos(i);
        if (Math.floor(i / 100) % 2 === 0) {
            lineOut.geometry.coordinates.push([pos.x, pos.y]);
        }
    }

    return lineOut;
};

},{"./spline.js":17,"turf-helpers":16}],16:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],17:[function(require,module,exports){
/* eslint-disable */

 /**
   * BezierSpline
   * https://github.com/leszekr/bezier-spline-js
   *
   * @private
   * @copyright
   * Copyright (c) 2013 Leszek Rybicki
   *
   * Permission is hereby granted, free of charge, to any person obtaining a copy
   * of this software and associated documentation files (the "Software"), to deal
   * in the Software without restriction, including without limitation the rights
   * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
   * copies of the Software, and to permit persons to whom the Software is
   * furnished to do so, subject to the following conditions:
   *
   * The above copyright notice and this permission notice shall be included in all
   * copies or substantial portions of the Software.
   *
   * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
   * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
   * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
   * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
   * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
   * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
   * SOFTWARE.
   */
var Spline = function (options) {
    this.points = options.points || [];
    this.duration = options.duration || 10000;
    this.sharpness = options.sharpness || 0.85;
    this.centers = [];
    this.controls = [];
    this.stepLength = options.stepLength || 60;
    this.length = this.points.length;
    this.delay = 0;
    // this is to ensure compatibility with the 2d version
    for (var i = 0; i < this.length; i++) this.points[i].z = this.points[i].z || 0;
    for (var i = 0; i < this.length - 1; i++) {
        var p1 = this.points[i];
        var p2 = this.points[i + 1];
        this.centers.push({
            x: (p1.x + p2.x) / 2,
            y: (p1.y + p2.y) / 2,
            z: (p1.z + p2.z) / 2
        });
    }
    this.controls.push([this.points[0], this.points[0]]);
    for (var i = 0; i < this.centers.length - 1; i++) {
        var p1 = this.centers[i];
        var p2 = this.centers[i + 1];
        var dx = this.points[i + 1].x - (this.centers[i].x + this.centers[i + 1].x) / 2;
        var dy = this.points[i + 1].y - (this.centers[i].y + this.centers[i + 1].y) / 2;
        var dz = this.points[i + 1].z - (this.centers[i].y + this.centers[i + 1].z) / 2;
        this.controls.push([{
            x: (1.0 - this.sharpness) * this.points[i + 1].x + this.sharpness * (this.centers[i].x + dx),
            y: (1.0 - this.sharpness) * this.points[i + 1].y + this.sharpness * (this.centers[i].y + dy),
            z: (1.0 - this.sharpness) * this.points[i + 1].z + this.sharpness * (this.centers[i].z + dz)},
            {
                x: (1.0 - this.sharpness) * this.points[i + 1].x + this.sharpness * (this.centers[i + 1].x + dx),
                y: (1.0 - this.sharpness) * this.points[i + 1].y + this.sharpness * (this.centers[i + 1].y + dy),
                z: (1.0 - this.sharpness) * this.points[i + 1].z + this.sharpness * (this.centers[i + 1].z + dz)}]);
    }
    this.controls.push([this.points[this.length - 1], this.points[this.length - 1]]);
    this.steps = this.cacheSteps(this.stepLength);
    return this;
};

  /*
    Caches an array of equidistant (more or less) points on the curve.
  */
Spline.prototype.cacheSteps = function (mindist) {
    var steps = [];
    var laststep = this.pos(0);
    steps.push(0);
    for (var t = 0; t < this.duration; t += 10) {
        var step = this.pos(t);
        var dist = Math.sqrt((step.x - laststep.x) * (step.x - laststep.x) + (step.y - laststep.y) * (step.y - laststep.y) + (step.z - laststep.z) * (step.z - laststep.z));
        if (dist > mindist) {
            steps.push(t);
            laststep = step;
        }
    }
    return steps;
};

  /*
    returns angle and speed in the given point in the curve
  */
Spline.prototype.vector = function (t) {
    var p1 = this.pos(t + 10);
    var p2 = this.pos(t - 10);
    return {
        angle:180 * Math.atan2(p1.y - p2.y, p1.x - p2.x) / 3.14,
        speed:Math.sqrt((p2.x - p1.x) * (p2.x - p1.x) + (p2.y - p1.y) * (p2.y - p1.y) + (p2.z - p1.z) * (p2.z - p1.z))
    };
};

  /*
    Gets the position of the point, given time.

    WARNING: The speed is not constant. The time it takes between control points is constant.

    For constant speed, use Spline.steps[i];
  */
Spline.prototype.pos = function (time) {

    function bezier(t, p1, c1, c2, p2) {
        var B = function (t) {
            var t2 = t * t, t3 = t2 * t;
            return [(t3), (3 * t2 * (1 - t)), (3 * t * (1 - t) * (1 - t)), ((1 - t) * (1 - t) * (1 - t))];
        };
        var b = B(t);
        var pos = {
            x : p2.x * b[0] + c2.x * b[1] + c1.x * b[2] + p1.x * b[3],
            y : p2.y * b[0] + c2.y * b[1] + c1.y * b[2] + p1.y * b[3],
            z : p2.z * b[0] + c2.z * b[1] + c1.z * b[2] + p1.z * b[3]
        };
        return pos;
    }
    var t = time - this.delay;
    if (t < 0) t = 0;
    if (t > this.duration) t = this.duration - 1;
    //t = t-this.delay;
    var t2 = (t) / this.duration;
    if (t2 >= 1) return this.points[this.length - 1];

    var n = Math.floor((this.points.length - 1) * t2);
    var t1 = (this.length - 1) * t2 - n;
    return bezier(t1, this.points[n], this.controls[n][1], this.controls[n + 1][0], this.points[n + 1]);
};

module.exports = Spline;

},{}],18:[function(require,module,exports){
// http://stackoverflow.com/questions/839899/how-do-i-calculate-a-point-on-a-circles-circumference
// radians = degrees * (pi/180)
// https://github.com/bjornharrtell/jsts/blob/master/examples/buffer.html

var helpers = require('turf-helpers');
var featureCollection = helpers.featureCollection;
var jsts = require('jsts');
var normalize = require('geojson-normalize');

/**
 * Calculates a buffer for input features for a given radius. Units supported are miles, kilometers, and degrees.
 *
 * @name buffer
 * @param {(Feature|FeatureCollection)} feature input to be buffered
 * @param {number} distance distance to draw the buffer
 * @param {string} unit any of the options supported by turf units
 * @return {FeatureCollection<Polygon>|FeatureCollection<MultiPolygon>|Polygon|MultiPolygon} buffered features
 *
 * @example
 * var pt = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-90.548630, 14.616599]
 *   }
 * };
 * var unit = 'miles';
 *
 * var buffered = turf.buffer(pt, 500, unit);
 * var result = turf.featurecollection([buffered, pt]);
 *
 * //=result
 */

module.exports = function (feature, radius, units) {

    var degrees = helpers.distanceToDegrees(radius, units);
    var fc = normalize(feature);
    var buffered = normalize(featureCollection(fc.features.map(function (f) {
        return bufferOp(f, degrees);
    })));

    if (buffered.features.length > 1) return buffered;
    else if (buffered.features.length === 1) return buffered.features[0];
};

function bufferOp(feature, radius) {
    var reader = new jsts.io.GeoJSONReader();
    var geom = reader.read(feature.geometry);
    var buffered = geom.buffer(radius);
    var writer = new jsts.io.GeoJSONWriter();
    buffered = writer.write(buffered);

    return {
        type: 'Feature',
        geometry: buffered,
        properties: {}
    };
}

},{"geojson-normalize":19,"jsts":20,"turf-helpers":21}],19:[function(require,module,exports){
module.exports = normalize;

var types = {
    Point: 'geometry',
    MultiPoint: 'geometry',
    LineString: 'geometry',
    MultiLineString: 'geometry',
    Polygon: 'geometry',
    MultiPolygon: 'geometry',
    GeometryCollection: 'geometry',
    Feature: 'feature',
    FeatureCollection: 'featurecollection'
};

/**
 * Normalize a GeoJSON feature into a FeatureCollection.
 *
 * @param {object} gj geojson data
 * @returns {object} normalized geojson data
 */
function normalize(gj) {
    if (!gj || !gj.type) return null;
    var type = types[gj.type];
    if (!type) return null;

    if (type === 'geometry') {
        return {
            type: 'FeatureCollection',
            features: [{
                type: 'Feature',
                properties: {},
                geometry: gj
            }]
        };
    } else if (type === 'feature') {
        return {
            type: 'FeatureCollection',
            features: [gj]
        };
    } else if (type === 'featurecollection') {
        return gj;
    }
}

},{}],20:[function(require,module,exports){
// JSTS. See https://github.com/bjornharrtell/jsts
// Licenses:
// https://github.com/bjornharrtell/jsts/blob/master/LICENSE_EDLv1.txt
// https://github.com/bjornharrtell/jsts/blob/master/LICENSE_EPLv1.txt
// https://github.com/bjornharrtell/jsts/blob/master/LICENSE_LICENSE_ES6_COLLECTIONS.txt
!function(t,e){"object"==typeof exports&&"undefined"!=typeof module?e(exports):"function"==typeof define&&define.amd?define(["exports"],e):e(t.jsts=t.jsts||{})}(this,function(t){"use strict";function e(t,e){for(var n in e)e.hasOwnProperty(n)&&(t[n]=e[n])}function n(){}function i(){}function r(){}function s(){}function o(){}function a(){}function u(){}function l(t){this.message=t}function h(t,e){t.prototype=Object.create(e.prototype),t.prototype.constructor=t}function c(){if(0===arguments.length)l.call(this);else if(1===arguments.length){var t=arguments[0];l.call(this,t)}}function f(){}function g(){if(this.x=null,this.y=null,this.z=null,0===arguments.length)g.call(this,0,0);else if(1===arguments.length){var t=arguments[0];g.call(this,t.x,t.y,t.z)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];g.call(this,e,n,g.NULL_ORDINATE)}else if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];this.x=i,this.y=r,this.z=s}}function d(){if(this.dimensionsToTest=2,0===arguments.length)d.call(this,2);else if(1===arguments.length){var t=arguments[0];if(2!==t&&3!==t)throw new i("only 2 or 3 dimensions may be specified");this.dimensionsToTest=t}}function p(){}function v(){}function m(t){this.message=t||""}function y(){}function x(t){this.message=t||""}function E(t){this.message=t||""}function I(){this.array_=[],arguments[0]instanceof v&&this.addAll(arguments[0])}function N(){if(I.apply(this),0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.ensureCapacity(t.length),this.add(t,!0)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.ensureCapacity(e.length),this.add(e,n)}}function C(){if(this.minx=null,this.maxx=null,this.miny=null,this.maxy=null,0===arguments.length)this.init();else if(1===arguments.length){if(arguments[0]instanceof g){var t=arguments[0];this.init(t.x,t.x,t.y,t.y)}else if(arguments[0]instanceof C){var e=arguments[0];this.init(e)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.init(n.x,i.x,n.y,i.y)}else if(4===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],a=arguments[3];this.init(r,s,o,a)}}function S(){}function w(){S.call(this,"Projective point not representable on the Cartesian plane.")}function L(){}function R(t,e){return t.interfaces_&&t.interfaces_().indexOf(e)>-1}function T(){}function P(t){this.str=t}function b(t){this.value=t}function O(){}function _(){if(this.hi=0,this.lo=0,0===arguments.length)this.init(0);else if(1===arguments.length){if("number"==typeof arguments[0]){var t=arguments[0];this.init(t)}else if(arguments[0]instanceof _){var e=arguments[0];this.init(e)}else if("string"==typeof arguments[0]){var n=arguments[0];_.call(this,_.parse(n))}}else if(2===arguments.length){var i=arguments[0],r=arguments[1];this.init(i,r)}}function M(){}function D(){}function A(){}function F(){if(this.x=null,this.y=null,this.w=null,0===arguments.length)this.x=0,this.y=0,this.w=1;else if(1===arguments.length){var t=arguments[0];this.x=t.x,this.y=t.y,this.w=1}else if(2===arguments.length){if("number"==typeof arguments[0]&&"number"==typeof arguments[1]){var e=arguments[0],n=arguments[1];this.x=e,this.y=n,this.w=1}else if(arguments[0]instanceof F&&arguments[1]instanceof F){var i=arguments[0],r=arguments[1];this.x=i.y*r.w-r.y*i.w,this.y=r.x*i.w-i.x*r.w,this.w=i.x*r.y-r.x*i.y}else if(arguments[0]instanceof g&&arguments[1]instanceof g){var s=arguments[0],o=arguments[1];this.x=s.y-o.y,this.y=o.x-s.x,this.w=s.x*o.y-o.x*s.y}}else if(3===arguments.length){var a=arguments[0],u=arguments[1],l=arguments[2];this.x=a,this.y=u,this.w=l}else if(4===arguments.length){var h=arguments[0],c=arguments[1],f=arguments[2],d=arguments[3],p=h.y-c.y,v=c.x-h.x,m=h.x*c.y-c.x*h.y,y=f.y-d.y,x=d.x-f.x,E=f.x*d.y-d.x*f.y;this.x=v*E-x*m,this.y=y*m-p*E,this.w=p*x-y*v}}function G(){}function q(){}function B(){this.envelope=null,this.factory=null,this.SRID=null,this.userData=null;var t=arguments[0];this.factory=t,this.SRID=t.getSRID()}function z(){}function V(){}function k(){}function Y(){}function U(){}function X(){}function H(){}function W(){}function j(){}function K(){}function Z(){}function Q(){}function J(){this.array_=[],arguments[0]instanceof v&&this.addAll(arguments[0])}function $(t){return null==t?$s:t.color}function tt(t){return null==t?null:t.parent}function et(t,e){null!==t&&(t.color=e)}function nt(t){return null==t?null:t.left}function it(t){return null==t?null:t.right}function rt(){this.root_=null,this.size_=0}function st(){}function ot(){}function at(){this.array_=[],arguments[0]instanceof v&&this.addAll(arguments[0])}function ut(){}function lt(){}function ht(){}function ct(){}function ft(){this.geometries=null;var t=arguments[0],e=arguments[1];if(B.call(this,e),null===t&&(t=[]),B.hasNullElements(t))throw new i("geometries must not contain null elements");this.geometries=t}function gt(){var t=arguments[0],e=arguments[1];ft.call(this,t,e)}function dt(){if(this.geom=null,this.geomFact=null,this.bnRule=null,this.endpointMap=null,1===arguments.length){var t=arguments[0];dt.call(this,t,V.MOD2_BOUNDARY_RULE)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.geom=e,this.geomFact=e.getFactory(),this.bnRule=n}}function pt(){this.count=null}function vt(){}function mt(){}function yt(){}function xt(){}function Et(){}function It(){}function Nt(){}function Ct(){}function St(){this.points=null;var t=arguments[0],e=arguments[1];B.call(this,e),this.init(t)}function wt(){}function Lt(){this.coordinates=null;var t=arguments[0],e=arguments[1];B.call(this,e),this.init(t)}function Rt(){}function Tt(){this.shell=null,this.holes=null;var t=arguments[0],e=arguments[1],n=arguments[2];if(B.call(this,n),null===t&&(t=this.getFactory().createLinearRing()),null===e&&(e=[]),B.hasNullElements(e))throw new i("holes must not contain null elements");if(t.isEmpty()&&B.hasNonEmptyElements(e))throw new i("shell is empty but holes are not");this.shell=t,this.holes=e}function Pt(){var t=arguments[0],e=arguments[1];ft.call(this,t,e)}function bt(){if(arguments[0]instanceof g&&arguments[1]instanceof ie){var t=arguments[0],e=arguments[1];bt.call(this,e.getCoordinateSequenceFactory().create(t),e)}else if(R(arguments[0],D)&&arguments[1]instanceof ie){var n=arguments[0],i=arguments[1];St.call(this,n,i),this.validateConstruction()}}function Ot(){var t=arguments[0],e=arguments[1];ft.call(this,t,e)}function _t(){if(this.factory=null,this.isUserDataCopied=!1,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.factory=t}}function Mt(){}function Dt(){}function At(){}function Ft(){}function Gt(){if(this.dimension=3,this.coordinates=null,1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];Gt.call(this,t,3)}else if(Number.isInteger(arguments[0])){var e=arguments[0];this.coordinates=new Array(e).fill(null);for(var n=0;e>n;n++)this.coordinates[n]=new g}else if(R(arguments[0],D)){var i=arguments[0];if(null===i)return this.coordinates=new Array(0).fill(null),null;this.dimension=i.getDimension(),this.coordinates=new Array(i.size()).fill(null);for(var n=0;n<this.coordinates.length;n++)this.coordinates[n]=i.getCoordinateCopy(n)}}else if(2===arguments.length)if(arguments[0]instanceof Array&&Number.isInteger(arguments[1])){var r=arguments[0],s=arguments[1];this.coordinates=r,this.dimension=s,null===r&&(this.coordinates=new Array(0).fill(null))}else if(Number.isInteger(arguments[0])&&Number.isInteger(arguments[1])){var o=arguments[0],a=arguments[1];this.coordinates=new Array(o).fill(null),this.dimension=a;for(var n=0;o>n;n++)this.coordinates[n]=new g}}function qt(){}function Bt(t,e){return t===e||t!==t&&e!==e}function zt(t,e){function n(t){return this&&this.constructor===n?(this._keys=[],this._values=[],this._itp=[],this.objectOnly=e,void(t&&Vt.call(this,t))):new n(t)}return e||io(t,"size",{get:Jt}),t.constructor=n,n.prototype=t,n}function Vt(t){this.add?t.forEach(this.add,this):t.forEach(function(t){this.set(t[0],t[1])},this)}function kt(t){return this.has(t)&&(this._keys.splice(no,1),this._values.splice(no,1),this._itp.forEach(function(t){no<t[0]&&t[0]--})),no>-1}function Yt(t){return this.has(t)?this._values[no]:void 0}function Ut(t,e){if(this.objectOnly&&e!==Object(e))throw new TypeError("Invalid value used as weak collection key");if(e!==e||0===e)for(no=t.length;no--&&!Bt(t[no],e););else no=t.indexOf(e);return no>-1}function Xt(t){return Ut.call(this,this._keys,t)}function Ht(t,e){return this.has(t)?this._values[no]=e:this._values[this._keys.push(t)-1]=e,this}function Wt(){(this._keys||0).length=this._values.length=0}function jt(){return Qt(this._itp,this._keys)}function Kt(){return Qt(this._itp,this._values)}function Zt(){return Qt(this._itp,this._keys,this._values)}function Qt(t,e,n){var i=[0],r=!1;return t.push(i),{next:function(){var s,o=i[0];return!r&&o<e.length?(s=n?[e[o],n[o]]:e[o],i[0]++):(r=!0,t.splice(t.indexOf(i),1)),{done:r,value:s}}}}function Jt(){return this._values.length}function $t(t,e){for(var n=this.entries();;){var i=n.next();if(i.done)break;t.call(e,i.value[1],i.value[0],this)}}function te(){this.map_=new so}function ee(){if(this.modelType=null,this.scale=null,0===arguments.length)this.modelType=ee.FLOATING;else if(1===arguments.length)if(arguments[0]instanceof ne){var t=arguments[0];this.modelType=t,t===ee.FIXED&&this.setScale(1)}else if("number"==typeof arguments[0]){var e=arguments[0];this.modelType=ee.FIXED,this.setScale(e)}else if(arguments[0]instanceof ee){var n=arguments[0];this.modelType=n.modelType,this.scale=n.scale}}function ne(){this.name=null;var t=arguments[0];this.name=t,ne.nameToTypeMap.put(t,this)}function ie(){if(this.precisionModel=null,this.coordinateSequenceFactory=null,this.SRID=null,0===arguments.length)ie.call(this,new ee,0);else if(1===arguments.length){if(R(arguments[0],G)){var t=arguments[0];ie.call(this,new ee,0,t)}else if(arguments[0]instanceof ee){var e=arguments[0];ie.call(this,e,0,ie.getDefaultCoordinateSequenceFactory())}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];ie.call(this,n,i,ie.getDefaultCoordinateSequenceFactory())}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];this.precisionModel=r,this.coordinateSequenceFactory=o,this.SRID=s}}function re(t){this.geometryFactory=t||new ie}function se(t){this.parser=new re(t)}function oe(){this.result=null,this.inputLines=Array(2).fill().map(function(){return Array(2)}),this.intPt=new Array(2).fill(null),this.intLineIndex=null,this._isProper=null,this.pa=null,this.pb=null,this.precisionModel=null,this.intPt[0]=new g,this.intPt[1]=new g,this.pa=this.intPt[0],this.pb=this.intPt[1],this.result=0}function ae(){oe.apply(this)}function ue(){}function le(){this.p=null,this.crossingCount=0,this.isPointOnSegment=!1;var t=arguments[0];this.p=t}function he(){}function ce(){if(this.p0=null,this.p1=null,0===arguments.length)ce.call(this,new g,new g);else if(1===arguments.length){var t=arguments[0];ce.call(this,t.p0,t.p1)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.p0=e,this.p1=n}else if(4===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2],o=arguments[3];ce.call(this,new g(i,r),new g(s,o))}}function fe(){if(this.matrix=null,0===arguments.length)this.matrix=Array(3).fill().map(function(){return Array(3)}),this.setAll(lt.FALSE);else if(1===arguments.length)if("string"==typeof arguments[0]){var t=arguments[0];fe.call(this),this.set(t)}else if(arguments[0]instanceof fe){var e=arguments[0];fe.call(this),this.matrix[L.INTERIOR][L.INTERIOR]=e.matrix[L.INTERIOR][L.INTERIOR],this.matrix[L.INTERIOR][L.BOUNDARY]=e.matrix[L.INTERIOR][L.BOUNDARY],this.matrix[L.INTERIOR][L.EXTERIOR]=e.matrix[L.INTERIOR][L.EXTERIOR],this.matrix[L.BOUNDARY][L.INTERIOR]=e.matrix[L.BOUNDARY][L.INTERIOR],this.matrix[L.BOUNDARY][L.BOUNDARY]=e.matrix[L.BOUNDARY][L.BOUNDARY],this.matrix[L.BOUNDARY][L.EXTERIOR]=e.matrix[L.BOUNDARY][L.EXTERIOR],this.matrix[L.EXTERIOR][L.INTERIOR]=e.matrix[L.EXTERIOR][L.INTERIOR],this.matrix[L.EXTERIOR][L.BOUNDARY]=e.matrix[L.EXTERIOR][L.BOUNDARY],this.matrix[L.EXTERIOR][L.EXTERIOR]=e.matrix[L.EXTERIOR][L.EXTERIOR]}}function ge(){this.areaBasePt=null,this.triangleCent3=new g,this.areasum2=0,this.cg3=new g,this.lineCentSum=new g,this.totalLength=0,this.ptCount=0,this.ptCentSum=new g;var t=arguments[0];this.areaBasePt=null,this.add(t)}function de(t){this.message=t||""}function pe(){this.array_=[]}function ve(){this.treeSet=new at,this.list=new I}function me(){if(this.geomFactory=null,this.inputPts=null,1===arguments.length){var t=arguments[0];me.call(this,me.extractCoordinates(t),t.getFactory())}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.inputPts=ve.filterCoordinates(e),this.geomFactory=n}}function ye(){this.origin=null;var t=arguments[0];this.origin=t}function xe(){this.inputGeom=null,this.factory=null,this.pruneEmptyGeometry=!0,this.preserveGeometryCollectionType=!0,this.preserveCollections=!1,this.preserveType=!1}function Ee(){if(this.snapTolerance=0,this.srcPts=null,this.seg=new ce,this.allowSnappingToSourceVertices=!1,this._isClosed=!1,arguments[0]instanceof St&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1];Ee.call(this,t.getCoordinates(),e)}else if(arguments[0]instanceof Array&&"number"==typeof arguments[1]){var n=arguments[0],i=arguments[1];this.srcPts=n,this._isClosed=Ee.isClosed(n),this.snapTolerance=i}}function Ie(){this.srcGeom=null;var t=arguments[0];this.srcGeom=t}function Ne(){if(xe.apply(this),this.snapTolerance=null,this.snapPts=null,this.isSelfSnap=!1,2===arguments.length){var t=arguments[0],e=arguments[1];this.snapTolerance=t,this.snapPts=e}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.snapTolerance=n,this.snapPts=i,this.isSelfSnap=r}}function Ce(){this.isFirst=!0,this.commonMantissaBitsCount=53,this.commonBits=0,this.commonSignExp=null}function Se(){this.commonCoord=null,this.ccFilter=new we}function we(){this.commonBitsX=new Ce,this.commonBitsY=new Ce}function Le(){this.trans=null;var t=arguments[0];this.trans=t}function Re(){this.parent=null,this.atStart=null,this.max=null,this.index=null,this.subcollectionIterator=null;var t=arguments[0];this.parent=t,this.atStart=!0,this.index=0,this.max=t.getNumGeometries()}function Te(){if(this.boundaryRule=V.OGC_SFS_BOUNDARY_RULE,this.isIn=null,this.numBoundaries=null,0===arguments.length);else if(1===arguments.length){var t=arguments[0];if(null===t)throw new i("Rule must be non-null");this.boundaryRule=t}}function Pe(){}function be(){}function Oe(){this.pts=null,this.data=null;var t=arguments[0],e=arguments[1];this.pts=t,this.data=e}function _e(){}function Me(){this.bounds=null,this.item=null;var t=arguments[0],e=arguments[1];this.bounds=t,this.item=e}function De(){this._size=null,this.items=null,this._size=0,this.items=new I,this.items.add(null)}function Ae(){}function Fe(){}function Ge(){if(this.childBoundables=new I,this.bounds=null,this.level=null,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.level=t}}function qe(){this.boundable1=null,this.boundable2=null,this._distance=null,this.itemDistance=null;var t=arguments[0],e=arguments[1],n=arguments[2];this.boundable1=t,this.boundable2=e,this.itemDistance=n,this._distance=this.distance()}function Be(){if(this.root=null,this.built=!1,this.itemBoundables=new I,this.nodeCapacity=null,0===arguments.length)Be.call(this,Be.DEFAULT_NODE_CAPACITY);else if(1===arguments.length){var t=arguments[0];f.isTrue(t>1,"Node capacity must be greater than 1"),this.nodeCapacity=t}}function ze(){}function Ve(){}function ke(){if(0===arguments.length)ke.call(this,ke.DEFAULT_NODE_CAPACITY);else if(1===arguments.length){var t=arguments[0];Be.call(this,t)}}function Ye(){var t=arguments[0];Ge.call(this,t)}function Ue(){}function Xe(){this.segString=null,this.coord=null,this.segmentIndex=null,this.segmentOctant=null,this._isInterior=null;var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];this.segString=t,this.coord=new g(e),this.segmentIndex=n,this.segmentOctant=i,this._isInterior=!e.equals2D(t.getCoordinate(n))}function He(){this.nodeMap=new rt,this.edge=null;var t=arguments[0];this.edge=t}function We(){this.nodeList=null,this.edge=null,this.nodeIt=null,this.currNode=null,this.nextNode=null,this.currSegIndex=0;var t=arguments[0];this.nodeList=t,this.edge=t.getEdge(),this.nodeIt=t.iterator(),this.readNextNode()}function je(){}function Ke(){this.nodeList=new He(this),this.pts=null,this.data=null;var t=arguments[0],e=arguments[1];this.pts=t,this.data=e}function Ze(){this.tempEnv1=new C,this.tempEnv2=new C,this.overlapSeg1=new ce,this.overlapSeg2=new ce}function Qe(){this.pts=null,this.start=null,this.end=null,this.env=null,this.context=null,this.id=null;var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];this.pts=t,this.start=e,this.end=n,this.context=i}function Je(){}function $e(){}function tn(){}function en(){if(this.segInt=null,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.setSegmentIntersector(t)}}function nn(){if(this.monoChains=new I,this.index=new ke,this.idCounter=0,this.nodedSegStrings=null,this.nOverlaps=0,0===arguments.length);else if(1===arguments.length){var t=arguments[0];en.call(this,t)}}function rn(){Ze.apply(this),this.si=null;var t=arguments[0];this.si=t}function sn(){if(this.pt=null,1===arguments.length){var t=arguments[0];l.call(this,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];l.call(this,sn.msgWithCoord(e,n)),this.pt=new g(n)}}function on(){}function an(){this.findAllIntersections=!1,this.isCheckEndSegmentsOnly=!1,this.li=null,this.interiorIntersection=null,this.intSegments=null,this.intersections=new I,this.intersectionCount=0,this.keepIntersections=!0;var t=arguments[0];this.li=t,this.interiorIntersection=null}function un(){this.li=new ae,this.segStrings=null,this.findAllIntersections=!1,this.segInt=null,this._isValid=!0;var t=arguments[0];this.segStrings=t}function ln(){this.nv=null;var t=arguments[0];this.nv=new un(ln.toSegmentStrings(t))}function hn(){this.mapOp=null;var t=arguments[0];this.mapOp=t}function cn(){}function fn(){if(this.location=null,1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];this.init(t.length)}else if(Number.isInteger(arguments[0])){var e=arguments[0];this.init(1),this.location[cn.ON]=e}else if(arguments[0]instanceof fn){var n=arguments[0];if(this.init(n.location.length),null!==n)for(var i=0;i<this.location.length;i++)this.location[i]=n.location[i]}}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];this.init(3),this.location[cn.ON]=r,this.location[cn.LEFT]=s,this.location[cn.RIGHT]=o}}function gn(){if(this.elt=new Array(2).fill(null),1===arguments.length){if(Number.isInteger(arguments[0])){var t=arguments[0];this.elt[0]=new fn(t),this.elt[1]=new fn(t)}else if(arguments[0]instanceof gn){var e=arguments[0];this.elt[0]=new fn(e.elt[0]),this.elt[1]=new fn(e.elt[1])}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.elt[0]=new fn(L.NONE),this.elt[1]=new fn(L.NONE),this.elt[n].setLocation(i)}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];this.elt[0]=new fn(r,s,o),this.elt[1]=new fn(r,s,o)}else if(4===arguments.length){var a=arguments[0],u=arguments[1],l=arguments[2],h=arguments[3];this.elt[0]=new fn(L.NONE,L.NONE,L.NONE),this.elt[1]=new fn(L.NONE,L.NONE,L.NONE),this.elt[a].setLocations(u,l,h)}}function dn(){this.startDe=null,this.maxNodeDegree=-1,this.edges=new I,this.pts=new I,this.label=new gn(L.NONE),this.ring=null,this._isHole=null,this.shell=null,this.holes=new I,this.geometryFactory=null;var t=arguments[0],e=arguments[1];this.geometryFactory=e,this.computePoints(t),this.computeRing()}function pn(){var t=arguments[0],e=arguments[1];dn.call(this,t,e)}function vn(){var t=arguments[0],e=arguments[1];dn.call(this,t,e)}function mn(){if(this.label=null,this._isInResult=!1,this._isCovered=!1,this._isCoveredSet=!1,this._isVisited=!1,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.label=t}}function yn(){mn.apply(this),this.coord=null,this.edges=null;var t=arguments[0],e=arguments[1];this.coord=t,this.edges=e,this.label=new gn(0,L.NONE)}function xn(){this.nodeMap=new rt,this.nodeFact=null;var t=arguments[0];this.nodeFact=t}function En(){if(this.edge=null,this.label=null,this.node=null,this.p0=null,this.p1=null,this.dx=null,this.dy=null,this.quadrant=null,1===arguments.length){var t=arguments[0];this.edge=t}else if(3===arguments.length){var e=arguments[0],n=arguments[1],i=arguments[2];En.call(this,e,n,i,null)}else if(4===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],a=arguments[3];En.call(this,r),this.init(s,o),this.label=a}}function In(){this._isForward=null,this._isInResult=!1,this._isVisited=!1,this.sym=null,this.next=null,this.nextMin=null,this.edgeRing=null,this.minEdgeRing=null,this.depth=[0,-999,-999];var t=arguments[0],e=arguments[1];if(En.call(this,t),this._isForward=e,e)this.init(t.getCoordinate(0),t.getCoordinate(1));else{var n=t.getNumPoints()-1;this.init(t.getCoordinate(n),t.getCoordinate(n-1))}this.computeDirectedLabel()}function Nn(){}function Cn(){if(this.edges=new I,this.nodes=null,this.edgeEndList=new I,0===arguments.length)this.nodes=new xn(new Nn);else if(1===arguments.length){var t=arguments[0];this.nodes=new xn(t)}}function Sn(){this.geometryFactory=null,this.shellList=new I;var t=arguments[0];this.geometryFactory=t}function wn(){this.op=null,this.geometryFactory=null,this.ptLocator=null,this.lineEdgesList=new I,this.resultLineList=new I;var t=arguments[0],e=arguments[1],n=arguments[2];this.op=t,this.geometryFactory=e,this.ptLocator=n}function Ln(){this.op=null,this.geometryFactory=null,this.resultPointList=new I;var t=arguments[0],e=arguments[1];arguments[2];this.op=t,this.geometryFactory=e}function Rn(){}function Tn(){this.geom=null;var t=arguments[0];this.geom=t}function Pn(){this.edgeMap=new rt,this.edgeList=null,this.ptInAreaLocation=[L.NONE,L.NONE]}function bn(){Pn.apply(this),this.resultAreaEdgeList=null,this.label=null,this.SCANNING_FOR_INCOMING=1,this.LINKING_TO_OUTGOING=2}function On(){Nn.apply(this)}function _n(){this.mce=null,this.chainIndex=null;var t=arguments[0],e=arguments[1];this.mce=t,this.chainIndex=e}function Mn(){if(this.label=null,this.xValue=null,this.eventType=null,this.insertEvent=null,this.deleteEventIndex=null,this.obj=null,2===arguments.length){var t=arguments[0],e=arguments[1];this.eventType=Mn.DELETE,this.xValue=t,this.insertEvent=e}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.eventType=Mn.INSERT,this.label=n,this.xValue=i,this.obj=r}}function Dn(){}function An(){this._hasIntersection=!1,this.hasProper=!1,this.hasProperInterior=!1,this.properIntersectionPoint=null,this.li=null,this.includeProper=null,this.recordIsolated=null,this.isSelfIntersection=null,this.numIntersections=0,this.numTests=0,this.bdyNodes=null,this._isDone=!1,this.isDoneWhenProperInt=!1;var t=arguments[0],e=arguments[1],n=arguments[2];this.li=t,this.includeProper=e,this.recordIsolated=n}function Fn(){Dn.apply(this),this.events=new I,this.nOverlaps=null}function Gn(){this.min=r.POSITIVE_INFINITY,this.max=r.NEGATIVE_INFINITY}function qn(){}function Bn(){Gn.apply(this),this.item=null;var t=arguments[0],e=arguments[1],n=arguments[2];this.min=t,this.max=e,this.item=n}function zn(){Gn.apply(this),this.node1=null,this.node2=null;var t=arguments[0],e=arguments[1];this.node1=t,this.node2=e,this.buildExtent(this.node1,this.node2)}function Vn(){this.leaves=new I,this.root=null,this.level=0}function kn(){if(this.lines=null,this.isForcedToLineString=!1,1===arguments.length){var t=arguments[0];this.lines=t}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.lines=e,this.isForcedToLineString=n}}function Yn(){this.items=new I}function Un(){this.index=null;var t=arguments[0];if(!R(t,Rt))throw new i("Argument must be Polygonal");this.index=new Hn(t)}function Xn(){this.counter=null;var t=arguments[0];this.counter=t}function Hn(){this.index=new Vn;var t=arguments[0];this.init(t)}function Wn(){this.coord=null,this.segmentIndex=null,this.dist=null;var t=arguments[0],e=arguments[1],n=arguments[2];this.coord=new g(t),this.segmentIndex=e,this.dist=n}function jn(){this.nodeMap=new rt,this.edge=null;var t=arguments[0];this.edge=t}function Kn(){}function Zn(){this.e=null,this.pts=null,this.startIndex=null,this.env1=new C,this.env2=new C;var t=arguments[0];this.e=t,this.pts=t.getCoordinates();var e=new Kn;this.startIndex=e.getChainStartIndices(this.pts)}function Qn(){this.depth=Array(2).fill().map(function(){return Array(3)});for(var t=0;2>t;t++)for(var e=0;3>e;e++)this.depth[t][e]=Qn.NULL_VALUE}function Jn(){if(mn.apply(this),this.pts=null,this.env=null,this.eiList=new jn(this),this.name=null,this.mce=null,this._isIsolated=!0,this.depth=new Qn,this.depthDelta=0,1===arguments.length){var t=arguments[0];Jn.call(this,t,null)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.pts=e,this.label=n}}function $n(){if(Cn.apply(this),this.parentGeom=null,this.lineEdgeMap=new te,this.boundaryNodeRule=null,this.useBoundaryDeterminationRule=!0,this.argIndex=null,this.boundaryNodes=null,this._hasTooFewPoints=!1,this.invalidPoint=null,this.areaPtLocator=null,this.ptLocator=new Te,2===arguments.length){var t=arguments[0],e=arguments[1];$n.call(this,t,e,V.OGC_SFS_BOUNDARY_RULE)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.argIndex=n,this.parentGeom=i,this.boundaryNodeRule=r,null!==i&&this.add(i)}}function ti(){if(this.li=new ae,this.resultPrecisionModel=null,this.arg=null,1===arguments.length){var t=arguments[0];this.setComputationPrecision(t.getPrecisionModel()),this.arg=new Array(1).fill(null),this.arg[0]=new $n(0,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];ti.call(this,e,n,V.OGC_SFS_BOUNDARY_RULE)}else if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];i.getPrecisionModel().compareTo(r.getPrecisionModel())>=0?this.setComputationPrecision(i.getPrecisionModel()):this.setComputationPrecision(r.getPrecisionModel()),this.arg=new Array(2).fill(null),this.arg[0]=new $n(0,i,s),this.arg[1]=new $n(1,r,s)}}function ei(){this.pts=null,this._orientation=null;var t=arguments[0];this.pts=t,this._orientation=ei.orientation(t)}function ni(){this.edges=new I,this.ocaMap=new rt}function ii(){this.ptLocator=new Te,this.geomFact=null,this.resultGeom=null,this.graph=null,this.edgeList=new ni,this.resultPolyList=new I,this.resultLineList=new I,this.resultPointList=new I;var t=arguments[0],e=arguments[1];ti.call(this,t,e),this.graph=new Cn(new On),this.geomFact=t.getFactory()}function ri(){this.geom=new Array(2).fill(null),this.snapTolerance=null,this.cbr=null;var t=arguments[0],e=arguments[1];this.geom[0]=t,this.geom[1]=e,this.computeSnapTolerance()}function si(){this.geom=new Array(2).fill(null);var t=arguments[0],e=arguments[1];this.geom[0]=t,this.geom[1]=e}function oi(){this.factory=null,this.interiorPoint=null,this.maxWidth=0;var t=arguments[0];this.factory=t.getFactory(),this.add(t)}function ai(){this.poly=null,this.centreY=null,this.hiY=r.MAX_VALUE,this.loY=-r.MAX_VALUE;var t=arguments[0];this.poly=t,this.hiY=t.getEnvelopeInternal().getMaxY(),this.loY=t.getEnvelopeInternal().getMinY(),this.centreY=oi.avg(this.loY,this.hiY)}function ui(){this.centroid=null,this.minDistance=r.MAX_VALUE,this.interiorPoint=null;var t=arguments[0];this.centroid=t.getCentroid().getCoordinate(),this.addInterior(t),null===this.interiorPoint&&this.addEndpoints(t)}function li(){this.centroid=null,this.minDistance=r.MAX_VALUE,this.interiorPoint=null;var t=arguments[0];this.centroid=t.getCentroid().getCoordinate(),this.add(t)}function hi(){}function ci(){this.p0=null,this.p1=null,this.p2=null;var t=arguments[0],e=arguments[1],n=arguments[2];this.p0=t,this.p1=e,this.p2=n}function fi(){this.input=null,this.extremalPts=null,this.centre=null,this.radius=0;var t=arguments[0];this.input=t}function gi(){if(this.inputGeom=null,this.isConvex=null,this.convexHullPts=null,this.minBaseSeg=new ce,this.minWidthPt=null,this.minPtIndex=null,this.minWidth=0,1===arguments.length){var t=arguments[0];gi.call(this,t,!1)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.inputGeom=e,this.isConvex=n}}function di(){this.inputGeom=null,this.distanceTolerance=null;var t=arguments[0];this.inputGeom=t}function pi(){xe.apply(this),this.distanceTolerance=null;var t=arguments[0];this.distanceTolerance=t}function vi(){this._orig=null,this._sym=null,this._next=null;var t=arguments[0];this._orig=t}function mi(){this._isMarked=!1;var t=arguments[0];vi.call(this,t)}function yi(){this.vertexMap=new te}function xi(){this._isStart=!1;var t=arguments[0];mi.call(this,t)}function Ei(){yi.apply(this)}function Ii(){this.result=null,this.factory=null,this.graph=null,this.lines=new I,this.nodeEdgeStack=new pe,this.ringStartEdge=null,this.graph=new Ei}function Ni(){this.items=new I,this.subnode=new Array(4).fill(null)}function Ci(){}function Si(t,e){var n,i,r,s,o={32:{d:127,c:128,b:0,a:0},64:{d:32752,c:0,b:0,a:0}},a={32:8,64:11}[t];if(s||(n=0>e||0>1/e,isFinite(e)||(s=o[t],n&&(s.d+=1<<t/4-1),i=Math.pow(2,a)-1,r=0)),!s){for(i={32:127,64:1023}[t],r=Math.abs(e);r>=2;)i++,r/=2;for(;1>r&&i>0;)i--,r*=2;0>=i&&(r/=2),32===t&&i>254&&(s={d:n?255:127,c:128,b:0,a:0},i=Math.pow(2,a)-1,r=0)}return i}function wi(){this.pt=new g,this.level=0,this.env=null;var t=arguments[0];this.computeKey(t)}function Li(){Ni.apply(this),this.env=null,this.centrex=null,this.centrey=null,this.level=null;var t=arguments[0],e=arguments[1];this.env=t,this.level=e,this.centrex=(t.getMinX()+t.getMaxX())/2,this.centrey=(t.getMinY()+t.getMaxY())/2}function Ri(){}function Ti(){Ni.apply(this)}function Pi(){this.root=null,this.minExtent=1,this.root=new Ti}function bi(t){this.geometryFactory=t||new ie}function Oi(t){this.geometryFactory=t||new ie,this.precisionModel=this.geometryFactory.getPrecisionModel(),this.parser=new bi(this.geometryFactory)}function _i(){this.parser=new bi(this.geometryFactory)}function Mi(t){this.geometryFactory=t||new ie,this.precisionModel=this.geometryFactory.getPrecisionModel(),this.parser=new re(this.geometryFactory)}function Di(t){return[t.x,t.y]}function Ai(t){this.geometryFactory=t||new ie}function Fi(){if(this.noder=null,this.scaleFactor=null,this.offsetX=null,this.offsetY=null,this.isScaled=!1,2===arguments.length){var t=arguments[0],e=arguments[1];Fi.call(this,t,e,0,0)}else if(4===arguments.length){var n=arguments[0],i=arguments[1];arguments[2],arguments[3];this.noder=n,this.scaleFactor=i,this.isScaled=!this.isIntegerPrecision()}}function Gi(){if(this.inputGeom=null,this.isClosedEndpointsInInterior=!0,this.nonSimpleLocation=null,1===arguments.length){var t=arguments[0];this.inputGeom=t}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.inputGeom=e,this.isClosedEndpointsInInterior=!n.isInBoundary(2)}}function qi(){this.pt=null,this.isClosed=null,this.degree=null;var t=arguments[0];this.pt=t,this.isClosed=!1,this.degree=0}function Bi(){if(this.quadrantSegments=Bi.DEFAULT_QUADRANT_SEGMENTS,this.endCapStyle=Bi.CAP_ROUND,this.joinStyle=Bi.JOIN_ROUND,this.mitreLimit=Bi.DEFAULT_MITRE_LIMIT,this._isSingleSided=!1,this.simplifyFactor=Bi.DEFAULT_SIMPLIFY_FACTOR,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.setQuadrantSegments(t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.setQuadrantSegments(e),this.setEndCapStyle(n)}else if(4===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2],o=arguments[3];this.setQuadrantSegments(i),this.setEndCapStyle(r),this.setJoinStyle(s),this.setMitreLimit(o)}}function zi(){this.minIndex=-1,this.minCoord=null,this.minDe=null,this.orientedDe=null}function Vi(){this.array_=[]}function ki(){this.finder=null,this.dirEdgeList=new I,this.nodes=new I,this.rightMostCoord=null,this.env=null,this.finder=new zi}function Yi(){this.inputLine=null,
this.distanceTol=null,this.isDeleted=null,this.angleOrientation=he.COUNTERCLOCKWISE;var t=arguments[0];this.inputLine=t}function Ui(){this.ptList=null,this.precisionModel=null,this.minimimVertexDistance=0,this.ptList=new I}function Xi(){this.maxCurveSegmentError=0,this.filletAngleQuantum=null,this.closingSegLengthFactor=1,this.segList=null,this.distance=0,this.precisionModel=null,this.bufParams=null,this.li=null,this.s0=null,this.s1=null,this.s2=null,this.seg0=new ce,this.seg1=new ce,this.offset0=new ce,this.offset1=new ce,this.side=0,this._hasNarrowConcaveAngle=!1;var t=arguments[0],e=arguments[1],n=arguments[2];this.precisionModel=t,this.bufParams=e,this.li=new ae,this.filletAngleQuantum=Math.PI/2/e.getQuadrantSegments(),e.getQuadrantSegments()>=8&&e.getJoinStyle()===Bi.JOIN_ROUND&&(this.closingSegLengthFactor=Xi.MAX_CLOSING_SEG_LEN_FACTOR),this.init(n)}function Hi(){this.distance=0,this.precisionModel=null,this.bufParams=null;var t=arguments[0],e=arguments[1];this.precisionModel=t,this.bufParams=e}function Wi(){this.subgraphs=null,this.seg=new ce,this.cga=new he;var t=arguments[0];this.subgraphs=t}function ji(){this.upwardSeg=null,this.leftDepth=null;var t=arguments[0],e=arguments[1];this.upwardSeg=new ce(t),this.leftDepth=e}function Ki(){this.inputGeom=null,this.distance=null,this.curveBuilder=null,this.curveList=new I;var t=arguments[0],e=arguments[1],n=arguments[2];this.inputGeom=t,this.distance=e,this.curveBuilder=n}function Zi(){this._hasIntersection=!1,this.hasProper=!1,this.hasProperInterior=!1,this.hasInterior=!1,this.properIntersectionPoint=null,this.li=null,this.isSelfIntersection=null,this.numIntersections=0,this.numInteriorIntersections=0,this.numProperIntersections=0,this.numTests=0;var t=arguments[0];this.li=t}function Qi(){this.bufParams=null,this.workingPrecisionModel=null,this.workingNoder=null,this.geomFact=null,this.graph=null,this.edgeList=new ni;var t=arguments[0];this.bufParams=t}function Ji(){this.li=new ae,this.segStrings=null;var t=arguments[0];this.segStrings=t}function $i(){this.li=null,this.pt=null,this.originalPt=null,this.ptScaled=null,this.p0Scaled=null,this.p1Scaled=null,this.scaleFactor=null,this.minx=null,this.maxx=null,this.miny=null,this.maxy=null,this.corner=new Array(4).fill(null),this.safeEnv=null;var t=arguments[0],e=arguments[1],n=arguments[2];if(this.originalPt=t,this.pt=t,this.scaleFactor=e,this.li=n,0>=e)throw new i("Scale factor must be non-zero");1!==e&&(this.pt=new g(this.scale(t.x),this.scale(t.y)),this.p0Scaled=new g,this.p1Scaled=new g),this.initCorners(this.pt)}function tr(){this.tempEnv1=new C,this.selectedSegment=new ce}function er(){this.index=null;var t=arguments[0];this.index=t}function nr(){tr.apply(this),this.hotPixel=null,this.parentEdge=null,this.hotPixelVertexIndex=null,this._isNodeAdded=!1;var t=arguments[0],e=arguments[1],n=arguments[2];this.hotPixel=t,this.parentEdge=e,this.hotPixelVertexIndex=n}function ir(){this.li=null,this.interiorIntersections=null;var t=arguments[0];this.li=t,this.interiorIntersections=new I}function rr(){this.pm=null,this.li=null,this.scaleFactor=null,this.noder=null,this.pointSnapper=null,this.nodedSegStrings=null;var t=arguments[0];this.pm=t,this.li=new ae,this.li.setPrecisionModel(t),this.scaleFactor=t.getScale()}function sr(){if(this.argGeom=null,this.distance=null,this.bufParams=new Bi,this.resultGeometry=null,this.saveException=null,1===arguments.length){var t=arguments[0];this.argGeom=t}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.argGeom=e,this.bufParams=n}}function or(){this.comps=null;var t=arguments[0];this.comps=t}function ar(){if(this.component=null,this.segIndex=null,this.pt=null,2===arguments.length){var t=arguments[0],e=arguments[1];ar.call(this,t,ar.INSIDE_AREA,e)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.component=n,this.segIndex=i,this.pt=r}}function ur(){this.pts=null;var t=arguments[0];this.pts=t}function lr(){this.locations=null;var t=arguments[0];this.locations=t}function hr(){if(this.geom=null,this.terminateDistance=0,this.ptLocator=new Te,this.minDistanceLocation=null,this.minDistance=r.MAX_VALUE,2===arguments.length){var t=arguments[0],e=arguments[1];hr.call(this,t,e,0)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],s=arguments[2];this.geom=new Array(2).fill(null),this.geom[0]=n,this.geom[1]=i,this.terminateDistance=s}}function cr(){this.factory=null,this.directedEdges=new I,this.coordinates=null;var t=arguments[0];this.factory=t}function fr(){this._isMarked=!1,this._isVisited=!1,this.data=null}function gr(){fr.apply(this),this.parentEdge=null,this.from=null,this.to=null,this.p0=null,this.p1=null,this.sym=null,this.edgeDirection=null,this.quadrant=null,this.angle=null;var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];this.from=t,this.to=e,this.edgeDirection=i,this.p0=t.getCoordinate(),this.p1=n;var r=this.p1.x-this.p0.x,s=this.p1.y-this.p0.y;this.quadrant=Je.quadrant(r,s),this.angle=Math.atan2(s,r)}function dr(){var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];gr.call(this,t,e,n,i)}function pr(){if(fr.apply(this),this.dirEdge=null,0===arguments.length);else if(2===arguments.length){var t=arguments[0],e=arguments[1];this.setDirectedEdges(t,e)}}function vr(){this.outEdges=new I,this.sorted=!1}function mr(){if(fr.apply(this),this.pt=null,this.deStar=null,1===arguments.length){var t=arguments[0];mr.call(this,t,new vr)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.pt=e,this.deStar=n}}function yr(){pr.apply(this),this.line=null;var t=arguments[0];this.line=t}function xr(){this.nodeMap=new rt}function Er(){this.edges=new J,this.dirEdges=new J,this.nodeMap=new xr}function Ir(){Er.apply(this)}function Nr(){this.graph=new Ir,this.mergedLineStrings=null,this.factory=null,this.edgeStrings=null}function Cr(){this.edgeRing=null,this.next=null,this.label=-1;var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];gr.call(this,t,e,n,i)}function Sr(){pr.apply(this),this.line=null;var t=arguments[0];this.line=t}function wr(){this.factory=null,this.deList=new I,this.lowestEdge=null,this.ring=null,this.ringPts=null,this.holes=null,this.shell=null,this._isHole=null,this._isProcessed=!1,this._isIncludedSet=!1,this._isIncluded=!1;var t=arguments[0];this.factory=t}function Lr(){}function Rr(){Er.apply(this),this.factory=null;var t=arguments[0];this.factory=t}function Tr(){if(this.lineStringAdder=new Pr(this),this.graph=null,this.dangles=new I,this.cutEdges=new I,this.invalidRingLines=new I,this.holeList=null,this.shellList=null,this.polyList=null,this.isCheckingRingsValid=!0,this.extractOnlyPolygonal=null,this.geomFactory=null,0===arguments.length)Tr.call(this,!1);else if(1===arguments.length){var t=arguments[0];this.extractOnlyPolygonal=t}}function Pr(){this.p=null;var t=arguments[0];this.p=t}function br(){}function Or(){if(this.edgeEnds=new I,1===arguments.length){var t=arguments[0];Or.call(this,null,t)}else if(2===arguments.length){var e=(arguments[0],arguments[1]);En.call(this,e.getEdge(),e.getCoordinate(),e.getDirectedCoordinate(),new gn(e.getLabel())),this.insert(e)}}function _r(){Pn.apply(this)}function Mr(){var t=arguments[0],e=arguments[1];yn.call(this,t,e)}function Dr(){Nn.apply(this)}function Ar(){this.li=new ae,this.ptLocator=new Te,this.arg=null,this.nodes=new xn(new Dr),this.im=null,this.isolatedEdges=new I,this.invalidPoint=null;var t=arguments[0];this.arg=t}function Fr(){this.rectEnv=null;var t=arguments[0];this.rectEnv=t.getEnvelopeInternal()}function Gr(){this.li=new ae,this.rectEnv=null,this.diagUp0=null,this.diagUp1=null,this.diagDown0=null,this.diagDown1=null;var t=arguments[0];this.rectEnv=t,this.diagUp0=new g(t.getMinX(),t.getMinY()),this.diagUp1=new g(t.getMaxX(),t.getMaxY()),this.diagDown0=new g(t.getMinX(),t.getMaxY()),this.diagDown1=new g(t.getMaxX(),t.getMinY())}function qr(){this._isDone=!1}function Br(){this.rectangle=null,this.rectEnv=null;var t=arguments[0];this.rectangle=t,this.rectEnv=t.getEnvelopeInternal()}function zr(){qr.apply(this),this.rectEnv=null,this._intersects=!1;var t=arguments[0];this.rectEnv=t}function Vr(){qr.apply(this),this.rectSeq=null,this.rectEnv=null,this._containsPoint=!1;var t=arguments[0];this.rectSeq=t.getExteriorRing().getCoordinateSequence(),this.rectEnv=t.getEnvelopeInternal()}function kr(){qr.apply(this),this.rectEnv=null,this.rectIntersector=null,this.hasIntersection=!1,this.p0=new g,this.p1=new g;var t=arguments[0];this.rectEnv=t.getEnvelopeInternal(),this.rectIntersector=new Gr(this.rectEnv)}function Yr(){if(this._relate=null,2===arguments.length){var t=arguments[0],e=arguments[1];ti.call(this,t,e),this._relate=new Ar(this.arg)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];ti.call(this,n,i,r),this._relate=new Ar(this.arg)}}function Ur(){this.geomFactory=null,this.skipEmpty=!1,this.inputGeoms=null;var t=arguments[0];this.geomFactory=Ur.extractFactory(t),this.inputGeoms=t}function Xr(){this.pointGeom=null,this.otherGeom=null,this.geomFact=null;var t=arguments[0],e=arguments[1];this.pointGeom=t,this.otherGeom=e,this.geomFact=e.getFactory()}function Hr(){this.sortIndex=-1,this.comps=null;var t=arguments[0],e=arguments[1];this.sortIndex=t,this.comps=e}function Wr(){this.inputPolys=null,this.geomFactory=null;var t=arguments[0];this.inputPolys=t,null===this.inputPolys&&(this.inputPolys=new I)}function jr(){if(this.polygons=new I,this.lines=new I,this.points=new I,this.geomFact=null,1===arguments.length){if(R(arguments[0],v)){var t=arguments[0];this.extract(t)}else if(arguments[0]instanceof B){var e=arguments[0];this.extract(e)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.geomFact=i,this.extract(n)}}function Kr(){this.geometryFactory=new ie,this.geomGraph=null,this.disconnectedRingcoord=null;var t=arguments[0];this.geomGraph=t}function Zr(){this.items=new I,this.subnode=[null,null]}function Qr(){if(this.min=null,this.max=null,0===arguments.length)this.min=0,this.max=0;else if(1===arguments.length){var t=arguments[0];this.init(t.min,t.max)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.init(e,n)}}function Jr(){this.pt=0,this.level=0,this.interval=null;var t=arguments[0];this.computeKey(t)}function $r(){Zr.apply(this),this.interval=null,this.centre=null,this.level=null;var t=arguments[0],e=arguments[1];this.interval=t,this.level=e,this.centre=(t.getMin()+t.getMax())/2}function ts(){Zr.apply(this)}function es(){this.root=null,this.minExtent=1,this.root=new ts}function ns(){}function is(){this.ring=null,this.tree=null,this.crossings=0,this.interval=new Qr;var t=arguments[0];this.ring=t,this.buildIndex()}function rs(){tr.apply(this),this.mcp=null,this.p=null;var t=arguments[0],e=arguments[1];this.mcp=t,this.p=e}function ss(){this.nodes=new xn(new Dr)}function os(){this.li=new ae,this.geomGraph=null,this.nodeGraph=new ss,this.invalidPoint=null;var t=arguments[0];this.geomGraph=t}function as(){this.graph=null,this.rings=new I,this.totalEnv=new C,this.index=null,this.nestedPt=null;var t=arguments[0];this.graph=t}function us(){if(this.errorType=null,this.pt=null,1===arguments.length){var t=arguments[0];us.call(this,t,null)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.errorType=e,null!==n&&(this.pt=n.copy())}}function ls(){this.parentGeometry=null,this.isSelfTouchingRingFormingHoleValid=!1,this.validErr=null;var t=arguments[0];this.parentGeometry=t}function hs(){_t.CoordinateOperation.apply(this),this.targetPM=null,this.removeCollapsed=!0;var t=arguments[0],e=arguments[1];this.targetPM=t,this.removeCollapsed=e}function cs(){this.targetPM=null,this.removeCollapsed=!0,this.changePrecisionModel=!1,this.isPointwise=!1;var t=arguments[0];this.targetPM=t}function fs(){this.pts=null,this.usePt=null,this.distanceTolerance=null,this.seg=new ce;var t=arguments[0];this.pts=t}function gs(){this.inputGeom=null,this.distanceTolerance=null,this.isEnsureValidTopology=!0;var t=arguments[0];this.inputGeom=t}function ds(){xe.apply(this),this.isEnsureValidTopology=!0,this.distanceTolerance=null;var t=arguments[0],e=arguments[1];this.isEnsureValidTopology=t,this.distanceTolerance=e}function ps(){if(this.parent=null,this.index=null,2===arguments.length){var t=arguments[0],e=arguments[1];ps.call(this,t,e,null,-1)}else if(4===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2],s=arguments[3];ce.call(this,n,i),this.parent=r,this.index=s}}function vs(){if(this.parentLine=null,this.segs=null,this.resultSegs=new I,this.minimumSize=null,1===arguments.length){var t=arguments[0];vs.call(this,t,2)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.parentLine=e,this.minimumSize=n,this.init()}}function ms(){this.index=new Pi}function ys(){this.querySeg=null,this.items=new I;var t=arguments[0];this.querySeg=t}function xs(){this.li=new ae,this.inputIndex=new ms,this.outputIndex=new ms,this.line=null,this.linePts=null,this.distanceTolerance=0;var t=arguments[0],e=arguments[1];this.inputIndex=t,this.outputIndex=e}function Es(){this.inputIndex=new ms,this.outputIndex=new ms,this.distanceTolerance=0}function Is(){this.inputGeom=null,this.lineSimplifier=new Es,this.linestringMap=null;var t=arguments[0];this.inputGeom=t}function Ns(){xe.apply(this),this.linestringMap=null;var t=arguments[0];this.linestringMap=t}function Cs(){this.tps=null;var t=arguments[0];this.tps=t}function Ss(){this.seg=null,this.segLen=null,this.splitPt=null,this.minimumLen=0;var t=arguments[0];this.seg=t,this.segLen=t.getLength()}function ws(){}function Ls(){}function Rs(){}function Ts(){if(this.p=null,1===arguments.length){var t=arguments[0];this.p=new g(t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.p=new g(e,n)}else if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];this.p=new g(i,r,s)}}function Ps(){this._isOnConstraint=null,this.constraint=null;var t=arguments[0];Ts.call(this,t)}function bs(){this._rot=null,this.vertex=null,this.next=null,this.data=null}function Os(){this.subdiv=null,this.isUsingTolerance=!1;var t=arguments[0];this.subdiv=t,this.isUsingTolerance=t.getTolerance()>0}function _s(){}function Ms(){this.subdiv=null,this.lastEdge=null;var t=arguments[0];this.subdiv=t,this.init()}function Ds(){if(this.seg=null,1===arguments.length){if("string"==typeof arguments[0]){var t=arguments[0];l.call(this,t)}else if(arguments[0]instanceof ce){var e=arguments[0];l.call(this,"Locate failed to converge (at edge: "+e+").  Possible causes include invalid Subdivision topology or very close sites"),this.seg=new ce(e)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];l.call(this,Ds.msgWithSpatial(n,i)),this.seg=new ce(i)}}function As(){}function Fs(){this.visitedKey=0,this.quadEdges=new I,this.startingEdge=null,this.tolerance=null,this.edgeCoincidenceTolerance=null,this.frameVertex=new Array(3).fill(null),this.frameEnv=null,this.locator=null,this.seg=new ce,this.triEdges=new Array(3).fill(null);var t=arguments[0],e=arguments[1];this.tolerance=e,this.edgeCoincidenceTolerance=e/Fs.EDGE_COINCIDENCE_TOL_FACTOR,this.createFrame(t),this.startingEdge=this.initSubdiv(),this.locator=new Ms(this)}function Gs(){}function qs(){this.triList=new I}function Bs(){this.triList=new I}function zs(){this.coordList=new N,this.triCoords=new I}function Vs(){if(this.ls=null,this.data=null,2===arguments.length){var t=arguments[0],e=arguments[1];this.ls=new ce(t,e)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.ls=new ce(n,i),this.data=r}else if(6===arguments.length){var s=arguments[0],o=arguments[1],a=arguments[2],u=arguments[3],l=arguments[4],h=arguments[5];Vs.call(this,new g(s,o,a),new g(u,l,h))}else if(7===arguments.length){var c=arguments[0],f=arguments[1],d=arguments[2],p=arguments[3],v=arguments[4],m=arguments[5],y=arguments[6];Vs.call(this,new g(c,f,d),new g(p,v,m),y)}}function ks(){}function Ys(){if(this.p=null,this.data=null,this.left=null,this.right=null,this.count=null,2===arguments.length){var t=arguments[0],e=arguments[1];this.p=new g(t),this.left=null,this.right=null,this.count=1,this.data=e}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.p=new g(n,i),this.left=null,this.right=null,this.count=1,this.data=r}}function Us(){if(this.root=null,this.numberOfNodes=null,this.tolerance=null,0===arguments.length)Us.call(this,0);else if(1===arguments.length){var t=arguments[0];this.tolerance=t}}function Xs(){this.tolerance=null,this.matchNode=null,this.matchDist=0,this.p=null;var t=arguments[0],e=arguments[1];this.p=t,this.tolerance=e}function Hs(){this.initialVertices=null,this.segVertices=null,this.segments=new I,this.subdiv=null,this.incDel=null,this.convexHull=null,this.splitFinder=new Ls,this.kdt=null,this.vertexFactory=null,this.computeAreaEnv=null,this.splitPt=null,this.tolerance=null;var t=arguments[0],e=arguments[1];this.initialVertices=new I(t),this.tolerance=e,this.kdt=new Us(e)}function Ws(){this.siteCoords=null,this.tolerance=0,this.subdiv=null}function js(){this.siteCoords=null,this.constraintLines=null,this.tolerance=0,this.subdiv=null,this.constraintVertexMap=new rt}function Ks(){this.siteCoords=null,this.tolerance=0,this.subdiv=null,this.clipEnv=null,this.diagramEnv=null}function Zs(){}Array.prototype.fill||(Array.prototype.fill=function(t){for(var e=Object(this),n=parseInt(e.length,10),i=arguments[1],r=parseInt(i,10)||0,s=0>r?Math.max(n+r,0):Math.min(r,n),o=arguments[2],a=void 0===o?n:parseInt(o,10)||0,u=0>a?Math.max(n+a,0):Math.min(a,n);u>s;s++)e[s]=t;return e}),Number.isFinite=Number.isFinite||function(t){return"number"==typeof t&&isFinite(t)},Number.isInteger=Number.isInteger||function(t){return"number"==typeof t&&isFinite(t)&&Math.floor(t)===t},Number.parseFloat=Number.parseFloat||parseFloat,Number.isNaN=Number.isNaN||function(t){return t!==t},Math.trunc=Math.trunc||function(t){return 0>t?Math.ceil(t):Math.floor(t)},e(n.prototype,{interfaces_:function(){return[]},getClass:function(){return n}}),n.equalsWithTolerance=function(t,e,n){return Math.abs(t-e)<=n},r.isNaN=function(t){return Number.isNaN(t)},r.doubleToLongBits=function(t){return t},r.longBitsToDouble=function(t){return t},r.isInfinite=function(t){return!Number.isFinite(t)},r.MAX_VALUE=Number.MAX_VALUE,h(c,l),e(c.prototype,{interfaces_:function(){return[]},getClass:function(){return c}}),e(f.prototype,{interfaces_:function(){return[]},getClass:function(){return f}}),f.shouldNeverReachHere=function(){if(0===arguments.length)f.shouldNeverReachHere(null);else if(1===arguments.length){var t=arguments[0];throw new c("Should never reach here"+(null!==t?": "+t:""))}},f.isTrue=function(){if(1===arguments.length){var t=arguments[0];f.isTrue(t,null)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(!e)throw null===n?new c:new c(n)}},f.equals=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];f.equals(t,e,null)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];if(!i.equals(n))throw new c("Expected "+n+" but encountered "+i+(null!==r?": "+r:""))}},e(g.prototype,{setOrdinate:function(t,e){switch(t){case g.X:this.x=e;break;case g.Y:this.y=e;break;case g.Z:this.z=e;break;default:throw new i("Invalid ordinate index: "+t)}},equals2D:function(){if(1===arguments.length){var t=arguments[0];return this.x!==t.x?!1:this.y===t.y}if(2===arguments.length){var e=arguments[0],i=arguments[1];return n.equalsWithTolerance(this.x,e.x,i)?!!n.equalsWithTolerance(this.y,e.y,i):!1}},getOrdinate:function(t){switch(t){case g.X:return this.x;case g.Y:return this.y;case g.Z:return this.z}throw new i("Invalid ordinate index: "+t)},equals3D:function(t){return this.x===t.x&&this.y===t.y&&(this.z===t.z||r.isNaN(this.z)&&r.isNaN(t.z))},equals:function(t){return t instanceof g?this.equals2D(t):!1},equalInZ:function(t,e){return n.equalsWithTolerance(this.z,t.z,e)},compareTo:function(t){var e=t;return this.x<e.x?-1:this.x>e.x?1:this.y<e.y?-1:this.y>e.y?1:0},clone:function(){try{var t=null;return t}catch(t){if(t instanceof CloneNotSupportedException)return f.shouldNeverReachHere("this shouldn't happen because this class is Cloneable"),null;throw t}finally{}},copy:function(){return new g(this)},toString:function(){return"("+this.x+", "+this.y+", "+this.z+")"},distance3D:function(t){var e=this.x-t.x,n=this.y-t.y,i=this.z-t.z;return Math.sqrt(e*e+n*n+i*i)},distance:function(t){var e=this.x-t.x,n=this.y-t.y;return Math.sqrt(e*e+n*n)},hashCode:function(){var t=17;return t=37*t+g.hashCode(this.x),t=37*t+g.hashCode(this.y)},setCoordinate:function(t){this.x=t.x,this.y=t.y,this.z=t.z},interfaces_:function(){return[s,o,u]},getClass:function(){return g}}),g.hashCode=function(){if(1===arguments.length){var t=arguments[0],e=r.doubleToLongBits(t);return Math.trunc(e^e>>>32)}},e(d.prototype,{compare:function(t,e){var n=t,i=e,r=d.compare(n.x,i.x);if(0!==r)return r;var s=d.compare(n.y,i.y);if(0!==s)return s;if(this.dimensionsToTest<=2)return 0;var o=d.compare(n.z,i.z);return o},interfaces_:function(){return[a]},getClass:function(){return d}}),d.compare=function(t,e){return e>t?-1:t>e?1:r.isNaN(t)?r.isNaN(e)?0:-1:r.isNaN(e)?1:0},g.DimensionalComparator=d,g.serialVersionUID=0x5cbf2c235c7e5800,g.NULL_ORDINATE=r.NaN,g.X=0,g.Y=1,g.Z=2,p.prototype.hasNext=function(){},p.prototype.next=function(){},p.prototype.remove=function(){},v.prototype.add=function(){},v.prototype.addAll=function(){},v.prototype.isEmpty=function(){},v.prototype.iterator=function(){},v.prototype.size=function(){},v.prototype.toArray=function(){},v.prototype.remove=function(){},m.prototype=new Error,m.prototype.name="IndexOutOfBoundsException",y.prototype=Object.create(v.prototype),y.prototype.constructor=y,y.prototype.get=function(){},y.prototype.set=function(){},y.prototype.isEmpty=function(){},x.prototype=new Error,x.prototype.name="NoSuchElementException",E.prototype=new Error,E.prototype.name="OperationNotSupported",I.prototype=Object.create(y.prototype),I.prototype.constructor=I,I.prototype.ensureCapacity=function(){},I.prototype.interfaces_=function(){return[y,v]},I.prototype.add=function(t){return this.array_.push(t),!0},I.prototype.clear=function(){this.array_=[]},I.prototype.addAll=function(t){for(var e=t.iterator();e.hasNext();)this.add(e.next());return!0},I.prototype.set=function(t,e){var n=this.array_[t];return this.array_[t]=e,n},I.prototype.iterator=function(){return new Qs(this)},I.prototype.get=function(t){if(0>t||t>=this.size())throw new m;return this.array_[t]},I.prototype.isEmpty=function(){return 0===this.array_.length},I.prototype.size=function(){return this.array_.length},I.prototype.toArray=function(){for(var t=[],e=0,n=this.array_.length;n>e;e++)t.push(this.array_[e]);return t},I.prototype.remove=function(t){for(var e=!1,n=0,i=this.array_.length;i>n;n++)if(this.array_[n]===t){this.array_.splice(n,1),e=!0;break}return e};var Qs=function(t){this.arrayList_=t,this.position_=0};Qs.prototype.next=function(){if(this.position_===this.arrayList_.size())throw new x;return this.arrayList_.get(this.position_++)},Qs.prototype.hasNext=function(){return this.position_<this.arrayList_.size()},Qs.prototype.set=function(t){return this.arrayList_.set(this.position_-1,t)},Qs.prototype.remove=function(){throw new E},h(N,I),e(N.prototype,{getCoordinate:function(t){return this.get(t)},addAll:function(){if(2===arguments.length){for(var t=arguments[0],e=arguments[1],n=!1,i=t.iterator();i.hasNext();)this.add(i.next(),e),n=!0;return n}return I.prototype.addAll.apply(this,arguments)},clone:function t(){for(var t=I.prototype.clone.call(this),e=0;e<this.size();e++)t.add(e,this.get(e).copy());return t},toCoordinateArray:function(){return this.toArray(N.coordArrayType)},add:function(){if(1===arguments.length){var t=arguments[0];I.prototype.add.call(this,t)}else if(2===arguments.length){if(arguments[0]instanceof Array&&"boolean"==typeof arguments[1]){var e=arguments[0],n=arguments[1];return this.add(e,n,!0),!0}if(arguments[0]instanceof g&&"boolean"==typeof arguments[1]){var i=arguments[0],r=arguments[1];if(!r&&this.size()>=1){var s=this.get(this.size()-1);if(s.equals2D(i))return null}I.prototype.add.call(this,i)}else if(arguments[0]instanceof Object&&"boolean"==typeof arguments[1]){var o=arguments[0],a=arguments[1];return this.add(o,a),!0}}else if(3===arguments.length){if("boolean"==typeof arguments[2]&&arguments[0]instanceof Array&&"boolean"==typeof arguments[1]){var u=arguments[0],l=arguments[1],h=arguments[2];if(h)for(var c=0;c<u.length;c++)this.add(u[c],l);else for(var c=u.length-1;c>=0;c--)this.add(u[c],l);return!0}if("boolean"==typeof arguments[2]&&Number.isInteger(arguments[0])&&arguments[1]instanceof g){var f=arguments[0],d=arguments[1],p=arguments[2];if(!p){var v=this.size();if(v>0){if(f>0){var m=this.get(f-1);if(m.equals2D(d))return null}if(v>f){var y=this.get(f);if(y.equals2D(d))return null}}}I.prototype.add.call(this,f,d)}}else if(4===arguments.length){var x=arguments[0],E=arguments[1],N=arguments[2],C=arguments[3],S=1;N>C&&(S=-1);for(var c=N;c!==C;c+=S)this.add(x[c],E);return!0}},closeRing:function(){this.size()>0&&this.add(new g(this.get(0)),!1)},interfaces_:function(){return[]},getClass:function(){return N}}),N.coordArrayType=new Array(0).fill(null),e(C.prototype,{getArea:function(){return this.getWidth()*this.getHeight()},equals:function(t){if(!(t instanceof C))return!1;var e=t;return this.isNull()?e.isNull():this.maxx===e.getMaxX()&&this.maxy===e.getMaxY()&&this.minx===e.getMinX()&&this.miny===e.getMinY()},intersection:function(t){if(this.isNull()||t.isNull()||!this.intersects(t))return new C;var e=this.minx>t.minx?this.minx:t.minx,n=this.miny>t.miny?this.miny:t.miny,i=this.maxx<t.maxx?this.maxx:t.maxx,r=this.maxy<t.maxy?this.maxy:t.maxy;return new C(e,i,n,r)},isNull:function(){return this.maxx<this.minx},getMaxX:function(){return this.maxx},covers:function(){if(1===arguments.length){if(arguments[0]instanceof g){var t=arguments[0];return this.covers(t.x,t.y)}if(arguments[0]instanceof C){var e=arguments[0];return this.isNull()||e.isNull()?!1:e.getMinX()>=this.minx&&e.getMaxX()<=this.maxx&&e.getMinY()>=this.miny&&e.getMaxY()<=this.maxy}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];return this.isNull()?!1:n>=this.minx&&n<=this.maxx&&i>=this.miny&&i<=this.maxy}},intersects:function(){if(1===arguments.length){if(arguments[0]instanceof C){var t=arguments[0];return this.isNull()||t.isNull()?!1:!(t.minx>this.maxx||t.maxx<this.minx||t.miny>this.maxy||t.maxy<this.miny)}if(arguments[0]instanceof g){var e=arguments[0];return this.intersects(e.x,e.y)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];return this.isNull()?!1:!(n>this.maxx||n<this.minx||i>this.maxy||i<this.miny)}},getMinY:function(){return this.miny},getMinX:function(){return this.minx},expandToInclude:function(){if(1===arguments.length){if(arguments[0]instanceof g){var t=arguments[0];this.expandToInclude(t.x,t.y)}else if(arguments[0]instanceof C){var e=arguments[0];if(e.isNull())return null;this.isNull()?(this.minx=e.getMinX(),this.maxx=e.getMaxX(),this.miny=e.getMinY(),this.maxy=e.getMaxY()):(e.minx<this.minx&&(this.minx=e.minx),e.maxx>this.maxx&&(this.maxx=e.maxx),e.miny<this.miny&&(this.miny=e.miny),e.maxy>this.maxy&&(this.maxy=e.maxy))}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.isNull()?(this.minx=n,this.maxx=n,this.miny=i,this.maxy=i):(n<this.minx&&(this.minx=n),n>this.maxx&&(this.maxx=n),i<this.miny&&(this.miny=i),i>this.maxy&&(this.maxy=i))}},minExtent:function(){if(this.isNull())return 0;var t=this.getWidth(),e=this.getHeight();return e>t?t:e},getWidth:function(){return this.isNull()?0:this.maxx-this.minx},compareTo:function(t){var e=t;return this.isNull()?e.isNull()?0:-1:e.isNull()?1:this.minx<e.minx?-1:this.minx>e.minx?1:this.miny<e.miny?-1:this.miny>e.miny?1:this.maxx<e.maxx?-1:this.maxx>e.maxx?1:this.maxy<e.maxy?-1:this.maxy>e.maxy?1:0},translate:function(t,e){return this.isNull()?null:void this.init(this.getMinX()+t,this.getMaxX()+t,this.getMinY()+e,this.getMaxY()+e)},toString:function(){return"Env["+this.minx+" : "+this.maxx+", "+this.miny+" : "+this.maxy+"]"},setToNull:function(){this.minx=0,this.maxx=-1,this.miny=0,this.maxy=-1},getHeight:function(){return this.isNull()?0:this.maxy-this.miny},maxExtent:function(){if(this.isNull())return 0;var t=this.getWidth(),e=this.getHeight();return t>e?t:e},expandBy:function(){if(1===arguments.length){var t=arguments[0];this.expandBy(t,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(this.isNull())return null;this.minx-=e,this.maxx+=e,this.miny-=n,this.maxy+=n,(this.minx>this.maxx||this.miny>this.maxy)&&this.setToNull()}},contains:function(){if(1===arguments.length){if(arguments[0]instanceof C){var t=arguments[0];return this.covers(t)}if(arguments[0]instanceof g){var e=arguments[0];return this.covers(e)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];return this.covers(n,i)}},centre:function(){return this.isNull()?null:new g((this.getMinX()+this.getMaxX())/2,(this.getMinY()+this.getMaxY())/2)},init:function(){if(0===arguments.length)this.setToNull();else if(1===arguments.length){if(arguments[0]instanceof g){var t=arguments[0];this.init(t.x,t.x,t.y,t.y)}else if(arguments[0]instanceof C){var e=arguments[0];this.minx=e.minx,this.maxx=e.maxx,this.miny=e.miny,this.maxy=e.maxy}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.init(n.x,i.x,n.y,i.y)}else if(4===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],a=arguments[3];s>r?(this.minx=r,this.maxx=s):(this.minx=s,this.maxx=r),a>o?(this.miny=o,this.maxy=a):(this.miny=a,this.maxy=o)}},getMaxY:function(){return this.maxy},distance:function(t){if(this.intersects(t))return 0;var e=0;this.maxx<t.minx?e=t.minx-this.maxx:this.minx>t.maxx&&(e=this.minx-t.maxx);var n=0;return this.maxy<t.miny?n=t.miny-this.maxy:this.miny>t.maxy&&(n=this.miny-t.maxy),0===e?n:0===n?e:Math.sqrt(e*e+n*n)},hashCode:function(){var t=17;return t=37*t+g.hashCode(this.minx),t=37*t+g.hashCode(this.maxx),t=37*t+g.hashCode(this.miny),t=37*t+g.hashCode(this.maxy)},interfaces_:function(){return[s,u]},getClass:function(){return C}}),C.intersects=function(){if(3===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2];return n.x>=(t.x<e.x?t.x:e.x)&&n.x<=(t.x>e.x?t.x:e.x)&&n.y>=(t.y<e.y?t.y:e.y)&&n.y<=(t.y>e.y?t.y:e.y)}if(4===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2],o=arguments[3],a=Math.min(s.x,o.x),u=Math.max(s.x,o.x),l=Math.min(i.x,r.x),h=Math.max(i.x,r.x);return l>u?!1:a>h?!1:(a=Math.min(s.y,o.y),u=Math.max(s.y,o.y),l=Math.min(i.y,r.y),h=Math.max(i.y,r.y),l>u?!1:!(a>h))}},C.serialVersionUID=0x51845cd552189800,h(w,S),e(w.prototype,{interfaces_:function(){return[]},getClass:function(){return w}}),e(L.prototype,{interfaces_:function(){return[]},getClass:function(){return L}}),L.toLocationSymbol=function(t){switch(t){case L.EXTERIOR:return"e";case L.BOUNDARY:return"b";case L.INTERIOR:return"i";case L.NONE:return"-"}throw new i("Unknown location value: "+t)},L.INTERIOR=0,L.BOUNDARY=1,L.EXTERIOR=2,L.NONE=-1,e(T.prototype,{interfaces_:function(){return[]},getClass:function(){return T}}),T.log10=function(t){var e=Math.log(t);return r.isInfinite(e)?e:r.isNaN(e)?e:e/T.LOG_10},T.min=function(t,e,n,i){var r=t;return r>e&&(r=e),r>n&&(r=n),r>i&&(r=i),r},T.clamp=function(){if("number"==typeof arguments[2]&&"number"==typeof arguments[0]&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1],n=arguments[2];return e>t?e:t>n?n:t}if(Number.isInteger(arguments[2])&&Number.isInteger(arguments[0])&&Number.isInteger(arguments[1])){var i=arguments[0],r=arguments[1],s=arguments[2];return r>i?r:i>s?s:i}},T.wrap=function(t,e){return 0>t?e- -t%e:t%e},T.max=function(){if(3===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],i=t;return e>i&&(i=e),n>i&&(i=n),i}if(4===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],a=arguments[3],i=r;return s>i&&(i=s),o>i&&(i=o),a>i&&(i=a),i}},T.average=function(t,e){
return(t+e)/2},T.LOG_10=Math.log(10),P.prototype.append=function(t){this.str+=t},P.prototype.setCharAt=function(t,e){return this.str.substr(0,t)+e+this.str.substr(t+1)},P.prototype.toString=function(t){return this.str},b.prototype.intValue=function(){return this.value},b.prototype.compareTo=function(t){return this.value<t?-1:this.value>t?1:0},b.isNaN=function(t){return Number.isNaN(t)},O.isWhitespace=function(t){return 32>=t&&t>=0||127==t},O.toUpperCase=function(t){return t.toUpperCase()},e(_.prototype,{le:function(t){return this.hi<t.hi||this.hi===t.hi&&this.lo<=t.lo},extractSignificantDigits:function(t,e){var n=this.abs(),i=_.magnitude(n.hi),r=_.TEN.pow(i);n=n.divide(r),n.gt(_.TEN)?(n=n.divide(_.TEN),i+=1):n.lt(_.ONE)&&(n=n.multiply(_.TEN),i-=1);for(var s=i+1,o=new P,a=_.MAX_PRINT_DIGITS-1,u=0;a>=u;u++){t&&u===s&&o.append(".");var l=Math.trunc(n.hi);if(0>l)break;var h=!1,c=0;l>9?(h=!0,c="9"):c="0"+l,o.append(c),n=n.subtract(_.valueOf(l)).multiply(_.TEN),h&&n.selfAdd(_.TEN);var f=!0,g=_.magnitude(n.hi);if(0>g&&Math.abs(g)>=a-u&&(f=!1),!f)break}return e[0]=i,o.toString()},sqr:function(){return this.multiply(this)},doubleValue:function(){return this.hi+this.lo},subtract:function(){if(arguments[0]instanceof _){var t=arguments[0];return this.add(t.negate())}if("number"==typeof arguments[0]){var e=arguments[0];return this.add(-e)}},equals:function(){if(1===arguments.length){var t=arguments[0];return this.hi===t.hi&&this.lo===t.lo}},isZero:function(){return 0===this.hi&&0===this.lo},selfSubtract:function(){if(arguments[0]instanceof _){var t=arguments[0];return this.isNaN()?this:this.selfAdd(-t.hi,-t.lo)}if("number"==typeof arguments[0]){var e=arguments[0];return this.isNaN()?this:this.selfAdd(-e,0)}},getSpecialNumberString:function(){return this.isZero()?"0.0":this.isNaN()?"NaN ":null},min:function(t){return this.le(t)?this:t},selfDivide:function(){if(1===arguments.length){if(arguments[0]instanceof _){var t=arguments[0];return this.selfDivide(t.hi,t.lo)}if("number"==typeof arguments[0]){var e=arguments[0];return this.selfDivide(e,0)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1],r=null,s=null,o=null,a=null,u=null,l=null,h=null,c=null;return u=this.hi/n,l=_.SPLIT*u,r=l-u,c=_.SPLIT*n,r=l-r,s=u-r,o=c-n,h=u*n,o=c-o,a=n-o,c=r*o-h+r*a+s*o+s*a,l=(this.hi-h-c+this.lo-u*i)/n,c=u+l,this.hi=c,this.lo=u-c+l,this}},dump:function(){return"DD<"+this.hi+", "+this.lo+">"},divide:function(){if(arguments[0]instanceof _){var t=arguments[0],e=null,n=null,i=null,s=null,o=null,a=null,u=null,l=null;o=this.hi/t.hi,a=_.SPLIT*o,e=a-o,l=_.SPLIT*t.hi,e=a-e,n=o-e,i=l-t.hi,u=o*t.hi,i=l-i,s=t.hi-i,l=e*i-u+e*s+n*i+n*s,a=(this.hi-u-l+this.lo-o*t.lo)/t.hi,l=o+a;var h=l,c=o-l+a;return new _(h,c)}if("number"==typeof arguments[0]){var f=arguments[0];return r.isNaN(f)?_.createNaN():_.copy(this).selfDivide(f,0)}},ge:function(t){return this.hi>t.hi||this.hi===t.hi&&this.lo>=t.lo},pow:function(t){if(0===t)return _.valueOf(1);var e=new _(this),n=_.valueOf(1),i=Math.abs(t);if(i>1)for(;i>0;)i%2===1&&n.selfMultiply(e),i/=2,i>0&&(e=e.sqr());else n=e;return 0>t?n.reciprocal():n},ceil:function(){if(this.isNaN())return _.NaN;var t=Math.ceil(this.hi),e=0;return t===this.hi&&(e=Math.ceil(this.lo)),new _(t,e)},compareTo:function(t){var e=t;return this.hi<e.hi?-1:this.hi>e.hi?1:this.lo<e.lo?-1:this.lo>e.lo?1:0},rint:function(){if(this.isNaN())return this;var t=this.add(.5);return t.floor()},setValue:function(){if(arguments[0]instanceof _){var t=arguments[0];return this.init(t),this}if("number"==typeof arguments[0]){var e=arguments[0];return this.init(e),this}},max:function(t){return this.ge(t)?this:t},sqrt:function(){if(this.isZero())return _.valueOf(0);if(this.isNegative())return _.NaN;var t=1/Math.sqrt(this.hi),e=this.hi*t,n=_.valueOf(e),i=this.subtract(n.sqr()),r=i.hi*(.5*t);return n.add(r)},selfAdd:function(){if(1===arguments.length){if(arguments[0]instanceof _){var t=arguments[0];return this.selfAdd(t.hi,t.lo)}if("number"==typeof arguments[0]){var e=arguments[0],n=null,i=null,r=null,s=null,o=null,a=null;return r=this.hi+e,o=r-this.hi,s=r-o,s=e-o+(this.hi-s),a=s+this.lo,n=r+a,i=a+(r-n),this.hi=n+i,this.lo=i+(n-this.hi),this}}else if(2===arguments.length){var u=arguments[0],l=arguments[1],n=null,i=null,h=null,c=null,r=null,s=null,o=null,a=null;r=this.hi+u,h=this.lo+l,o=r-this.hi,a=h-this.lo,s=r-o,c=h-a,s=u-o+(this.hi-s),c=l-a+(this.lo-c),o=s+h,n=r+o,i=o+(r-n),o=c+i;var f=n+o,g=o+(n-f);return this.hi=f,this.lo=g,this}},selfMultiply:function(){if(1===arguments.length){if(arguments[0]instanceof _){var t=arguments[0];return this.selfMultiply(t.hi,t.lo)}if("number"==typeof arguments[0]){var e=arguments[0];return this.selfMultiply(e,0)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1],r=null,s=null,o=null,a=null,u=null,l=null;u=_.SPLIT*this.hi,r=u-this.hi,l=_.SPLIT*n,r=u-r,s=this.hi-r,o=l-n,u=this.hi*n,o=l-o,a=n-o,l=r*o-u+r*a+s*o+s*a+(this.hi*i+this.lo*n);var h=u+l;r=u-h;var c=l+r;return this.hi=h,this.lo=c,this}},selfSqr:function(){return this.selfMultiply(this)},floor:function(){if(this.isNaN())return _.NaN;var t=Math.floor(this.hi),e=0;return t===this.hi&&(e=Math.floor(this.lo)),new _(t,e)},negate:function(){return this.isNaN()?this:new _(-this.hi,-this.lo)},clone:function(){try{return null}catch(t){if(t instanceof CloneNotSupportedException)return null;throw t}finally{}},multiply:function(){if(arguments[0]instanceof _){var t=arguments[0];return t.isNaN()?_.createNaN():_.copy(this).selfMultiply(t)}if("number"==typeof arguments[0]){var e=arguments[0];return r.isNaN(e)?_.createNaN():_.copy(this).selfMultiply(e,0)}},isNaN:function(){return r.isNaN(this.hi)},intValue:function(){return Math.trunc(this.hi)},toString:function(){var t=_.magnitude(this.hi);return t>=-3&&20>=t?this.toStandardNotation():this.toSciNotation()},toStandardNotation:function(){var t=this.getSpecialNumberString();if(null!==t)return t;var e=new Array(1).fill(null),n=this.extractSignificantDigits(!0,e),i=e[0]+1,r=n;if("."===n.charAt(0))r="0"+n;else if(0>i)r="0."+_.stringOfChar("0",-i)+n;else if(-1===n.indexOf(".")){var s=i-n.length,o=_.stringOfChar("0",s);r=n+o+".0"}return this.isNegative()?"-"+r:r},reciprocal:function(){var t=null,e=null,n=null,i=null,r=null,s=null,o=null,a=null;r=1/this.hi,s=_.SPLIT*r,t=s-r,a=_.SPLIT*this.hi,t=s-t,e=r-t,n=a-this.hi,o=r*this.hi,n=a-n,i=this.hi-n,a=t*n-o+t*i+e*n+e*i,s=(1-o-a-r*this.lo)/this.hi;var u=r+s,l=r-u+s;return new _(u,l)},toSciNotation:function(){if(this.isZero())return _.SCI_NOT_ZERO;var t=this.getSpecialNumberString();if(null!==t)return t;var e=new Array(1).fill(null),n=this.extractSignificantDigits(!1,e),i=_.SCI_NOT_EXPONENT_CHAR+e[0];if("0"===n.charAt(0))throw new IllegalStateException("Found leading zero: "+n);var r="";n.length>1&&(r=n.substring(1));var s=n.charAt(0)+"."+r;return this.isNegative()?"-"+s+i:s+i},abs:function(){return this.isNaN()?_.NaN:this.isNegative()?this.negate():new _(this)},isPositive:function(){return this.hi>0||0===this.hi&&this.lo>0},lt:function(t){return this.hi<t.hi||this.hi===t.hi&&this.lo<t.lo},add:function(){if(arguments[0]instanceof _){var t=arguments[0];return _.copy(this).selfAdd(t)}if("number"==typeof arguments[0]){var e=arguments[0];return _.copy(this).selfAdd(e)}},init:function(){if(1===arguments.length){if("number"==typeof arguments[0]){var t=arguments[0];this.hi=t,this.lo=0}else if(arguments[0]instanceof _){var e=arguments[0];this.hi=e.hi,this.lo=e.lo}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.hi=n,this.lo=i}},gt:function(t){return this.hi>t.hi||this.hi===t.hi&&this.lo>t.lo},isNegative:function(){return this.hi<0||0===this.hi&&this.lo<0},trunc:function(){return this.isNaN()?_.NaN:this.isPositive()?this.floor():this.ceil()},signum:function(){return this.hi>0?1:this.hi<0?-1:this.lo>0?1:this.lo<0?-1:0},interfaces_:function(){return[u,s,o]},getClass:function(){return _}}),_.sqr=function(t){return _.valueOf(t).selfMultiply(t)},_.valueOf=function(){if("string"==typeof arguments[0]){var t=arguments[0];return _.parse(t)}if("number"==typeof arguments[0]){var e=arguments[0];return new _(e)}},_.sqrt=function(t){return _.valueOf(t).sqrt()},_.parse=function(t){for(var e=0,n=t.length;O.isWhitespace(t.charAt(e));)e++;var i=!1;if(n>e){var r=t.charAt(e);"-"!==r&&"+"!==r||(e++,"-"===r&&(i=!0))}for(var s=new _,o=0,a=0,u=0;;){if(e>=n)break;var l=t.charAt(e);if(e++,O.isDigit(l)){var h=l-"0";s.selfMultiply(_.TEN),s.selfAdd(h),o++}else{if("."!==l){if("e"===l||"E"===l){var c=t.substring(e);try{u=b.parseInt(c)}catch(e){throw e instanceof NumberFormatException?new NumberFormatException("Invalid exponent "+c+" in string "+t):e}finally{}break}throw new NumberFormatException("Unexpected character '"+l+"' at position "+e+" in string "+t)}a=o}}var f=s,g=o-a-u;if(0===g)f=s;else if(g>0){var d=_.TEN.pow(g);f=s.divide(d)}else if(0>g){var d=_.TEN.pow(-g);f=s.multiply(d)}return i?f.negate():f},_.createNaN=function(){return new _(r.NaN,r.NaN)},_.copy=function(t){return new _(t)},_.magnitude=function(t){var e=Math.abs(t),n=Math.log(e)/Math.log(10),i=Math.trunc(Math.floor(n)),r=Math.pow(10,i);return e>=10*r&&(i+=1),i},_.stringOfChar=function(t,e){for(var n=new P,i=0;e>i;i++)n.append(t);return n.toString()},_.PI=new _(3.141592653589793,1.2246467991473532e-16),_.TWO_PI=new _(6.283185307179586,2.4492935982947064e-16),_.PI_2=new _(1.5707963267948966,6.123233995736766e-17),_.E=new _(2.718281828459045,1.4456468917292502e-16),_.NaN=new _(r.NaN,r.NaN),_.EPS=1.23259516440783e-32,_.SPLIT=134217729,_.MAX_PRINT_DIGITS=32,_.TEN=_.valueOf(10),_.ONE=_.valueOf(1),_.SCI_NOT_EXPONENT_CHAR="E",_.SCI_NOT_ZERO="0.0E0",e(M.prototype,{interfaces_:function(){return[]},getClass:function(){return M}}),M.orientationIndex=function(t,e,n){var i=M.orientationIndexFilter(t,e,n);if(1>=i)return i;var r=_.valueOf(e.x).selfAdd(-t.x),s=_.valueOf(e.y).selfAdd(-t.y),o=_.valueOf(n.x).selfAdd(-e.x),a=_.valueOf(n.y).selfAdd(-e.y);return r.selfMultiply(a).selfSubtract(s.selfMultiply(o)).signum()},M.signOfDet2x2=function(t,e,n,i){var r=t.multiply(i).selfSubtract(e.multiply(n));return r.signum()},M.intersection=function(t,e,n,i){var r=_.valueOf(i.y).selfSubtract(n.y).selfMultiply(_.valueOf(e.x).selfSubtract(t.x)),s=_.valueOf(i.x).selfSubtract(n.x).selfMultiply(_.valueOf(e.y).selfSubtract(t.y)),o=r.subtract(s),a=_.valueOf(i.x).selfSubtract(n.x).selfMultiply(_.valueOf(t.y).selfSubtract(n.y)),u=_.valueOf(i.y).selfSubtract(n.y).selfMultiply(_.valueOf(t.x).selfSubtract(n.x)),l=a.subtract(u),h=l.selfDivide(o).doubleValue(),c=_.valueOf(t.x).selfAdd(_.valueOf(e.x).selfSubtract(t.x).selfMultiply(h)).doubleValue(),f=_.valueOf(e.x).selfSubtract(t.x).selfMultiply(_.valueOf(t.y).selfSubtract(n.y)),d=_.valueOf(e.y).selfSubtract(t.y).selfMultiply(_.valueOf(t.x).selfSubtract(n.x)),p=f.subtract(d),v=p.selfDivide(o).doubleValue(),m=_.valueOf(n.y).selfAdd(_.valueOf(i.y).selfSubtract(n.y).selfMultiply(v)).doubleValue();return new g(c,m)},M.orientationIndexFilter=function(t,e,n){var i=null,r=(t.x-n.x)*(e.y-n.y),s=(t.y-n.y)*(e.x-n.x),o=r-s;if(r>0){if(0>=s)return M.signum(o);i=r+s}else{if(!(0>r))return M.signum(o);if(s>=0)return M.signum(o);i=-r-s}var a=M.DP_SAFE_EPSILON*i;return o>=a||-o>=a?M.signum(o):2},M.signum=function(t){return t>0?1:0>t?-1:0},M.DP_SAFE_EPSILON=1e-15,e(D.prototype,{setOrdinate:function(t,e,n){},size:function(){},getOrdinate:function(t,e){},getCoordinate:function(){if(1===arguments.length){arguments[0]}else if(2===arguments.length){arguments[0],arguments[1]}},getCoordinateCopy:function(t){},getDimension:function(){},getX:function(t){},clone:function(){},expandEnvelope:function(t){},copy:function(){},getY:function(t){},toCoordinateArray:function(){},interfaces_:function(){return[o]},getClass:function(){return D}}),D.X=0,D.Y=1,D.Z=2,D.M=3,A.arraycopy=function(t,e,n,i,r){for(var s=0,o=e;e+r>o;o++)n[i+s]=t[o],s++},A.getProperty=function(t){return{"line.separator":"\n"}[t]},e(F.prototype,{getY:function(){var t=this.y/this.w;if(r.isNaN(t)||r.isInfinite(t))throw new w;return t},getX:function(){var t=this.x/this.w;if(r.isNaN(t)||r.isInfinite(t))throw new w;return t},getCoordinate:function(){var t=new g;return t.x=this.getX(),t.y=this.getY(),t},interfaces_:function(){return[]},getClass:function(){return F}}),F.intersection=function(t,e,n,i){var s=t.y-e.y,o=e.x-t.x,a=t.x*e.y-e.x*t.y,u=n.y-i.y,l=i.x-n.x,h=n.x*i.y-i.x*n.y,c=o*h-l*a,f=u*a-s*h,d=s*l-u*o,p=c/d,v=f/d;if(r.isNaN(p)||r.isInfinite(p)||r.isNaN(v)||r.isInfinite(v))throw new w;return new g(p,v)},e(G.prototype,{create:function(){if(1===arguments.length){if(arguments[0]instanceof Array){arguments[0]}else if(R(arguments[0],D)){arguments[0]}}else if(2===arguments.length){arguments[0],arguments[1]}},interfaces_:function(){return[]},getClass:function(){return G}}),e(q.prototype,{filter:function(t){},interfaces_:function(){return[]},getClass:function(){return q}}),e(B.prototype,{isGeometryCollection:function(){return this.getSortIndex()===B.SORTINDEX_GEOMETRYCOLLECTION},getFactory:function(){return this.factory},getGeometryN:function(t){return this},getArea:function(){return 0},isRectangle:function(){return!1},equals:function(){if(1===arguments.length){if(arguments[0]instanceof B){var t=arguments[0];return null===t?!1:this.equalsTopo(t)}if(arguments[0]instanceof Object){var e=arguments[0];if(!(e instanceof B))return!1;var n=e;return this.equalsExact(n)}}},equalsExact:function(t){return this===t||this.equalsExact(t,0)},geometryChanged:function(){this.apply(B.geometryChangedFilter)},geometryChangedAction:function(){this.envelope=null},equalsNorm:function(t){return null===t?!1:this.norm().equalsExact(t.norm())},getLength:function(){return 0},getNumGeometries:function(){return 1},compareTo:function(){if(1===arguments.length){var t=arguments[0],e=t;return this.getSortIndex()!==e.getSortIndex()?this.getSortIndex()-e.getSortIndex():this.isEmpty()&&e.isEmpty()?0:this.isEmpty()?-1:e.isEmpty()?1:this.compareToSameClass(t)}if(2===arguments.length){var n=arguments[0],i=arguments[1],e=n;return this.getSortIndex()!==e.getSortIndex()?this.getSortIndex()-e.getSortIndex():this.isEmpty()&&e.isEmpty()?0:this.isEmpty()?-1:e.isEmpty()?1:this.compareToSameClass(n,i)}},getUserData:function(){return this.userData},getSRID:function(){return this.SRID},getEnvelope:function(){return this.getFactory().toGeometry(this.getEnvelopeInternal())},checkNotGeometryCollection:function(t){if(t.getSortIndex()===B.SORTINDEX_GEOMETRYCOLLECTION)throw new i("This method does not support GeometryCollection arguments")},equal:function(t,e,n){return 0===n?t.equals(e):t.distance(e)<=n},norm:function(){var t=this.copy();return t.normalize(),t},getPrecisionModel:function(){return this.factory.getPrecisionModel()},getEnvelopeInternal:function(){return null===this.envelope&&(this.envelope=this.computeEnvelopeInternal()),new C(this.envelope)},setSRID:function(t){this.SRID=t},setUserData:function(t){this.userData=t},compare:function(t,e){for(var n=t.iterator(),i=e.iterator();n.hasNext()&&i.hasNext();){var r=n.next(),s=i.next(),o=r.compareTo(s);if(0!==o)return o}return n.hasNext()?1:i.hasNext()?-1:0},hashCode:function(){return this.getEnvelopeInternal().hashCode()},isGeometryCollectionOrDerived:function(){return this.getSortIndex()===B.SORTINDEX_GEOMETRYCOLLECTION||this.getSortIndex()===B.SORTINDEX_MULTIPOINT||this.getSortIndex()===B.SORTINDEX_MULTILINESTRING||this.getSortIndex()===B.SORTINDEX_MULTIPOLYGON},interfaces_:function(){return[o,s,u]},getClass:function(){return B}}),B.hasNonEmptyElements=function(t){for(var e=0;e<t.length;e++)if(!t[e].isEmpty())return!0;return!1},B.hasNullElements=function(t){for(var e=0;e<t.length;e++)if(null===t[e])return!0;return!1},B.serialVersionUID=0x799ea46522854c00,B.SORTINDEX_POINT=0,B.SORTINDEX_MULTIPOINT=1,B.SORTINDEX_LINESTRING=2,B.SORTINDEX_LINEARRING=3,B.SORTINDEX_MULTILINESTRING=4,B.SORTINDEX_POLYGON=5,B.SORTINDEX_MULTIPOLYGON=6,B.SORTINDEX_GEOMETRYCOLLECTION=7,B.geometryChangedFilter={interfaces_:function(){return[q]},filter:function(t){t.geometryChangedAction()}},e(z.prototype,{filter:function(t){},interfaces_:function(){return[]},getClass:function(){return z}}),e(V.prototype,{isInBoundary:function(t){},interfaces_:function(){return[]},getClass:function(){return V}}),e(k.prototype,{isInBoundary:function(t){return t%2===1},interfaces_:function(){return[V]},getClass:function(){return k}}),e(Y.prototype,{isInBoundary:function(t){return t>0},interfaces_:function(){return[V]},getClass:function(){return Y}}),e(U.prototype,{isInBoundary:function(t){return t>1},interfaces_:function(){return[V]},getClass:function(){return U}}),e(X.prototype,{isInBoundary:function(t){return 1===t},interfaces_:function(){return[V]},getClass:function(){return X}}),V.Mod2BoundaryNodeRule=k,V.EndPointBoundaryNodeRule=Y,V.MultiValentEndPointBoundaryNodeRule=U,V.MonoValentEndPointBoundaryNodeRule=X,V.MOD2_BOUNDARY_RULE=new k,V.ENDPOINT_BOUNDARY_RULE=new Y,V.MULTIVALENT_ENDPOINT_BOUNDARY_RULE=new U,V.MONOVALENT_ENDPOINT_BOUNDARY_RULE=new X,V.OGC_SFS_BOUNDARY_RULE=V.MOD2_BOUNDARY_RULE,e(H.prototype,{interfaces_:function(){return[]},getClass:function(){return H}}),H.isRing=function(t){return t.length<4?!1:!!t[0].equals2D(t[t.length-1])},H.ptNotInList=function(t,e){for(var n=0;n<t.length;n++){var i=t[n];if(H.indexOf(i,e)<0)return i}return null},H.scroll=function(t,e){var n=H.indexOf(e,t);if(0>n)return null;var i=new Array(t.length).fill(null);A.arraycopy(t,n,i,0,t.length-n),A.arraycopy(t,0,i,t.length-n,n),A.arraycopy(i,0,t,0,t.length)},H.equals=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(t===e)return!0;if(null===t||null===e)return!1;if(t.length!==e.length)return!1;for(var n=0;n<t.length;n++)if(!t[n].equals(e[n]))return!1;return!0}if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];if(i===r)return!0;if(null===i||null===r)return!1;if(i.length!==r.length)return!1;for(var n=0;n<i.length;n++)if(0!==s.compare(i[n],r[n]))return!1;return!0}},H.intersection=function(t,e){for(var n=new N,i=0;i<t.length;i++)e.intersects(t[i])&&n.add(t[i],!0);return n.toCoordinateArray()},H.hasRepeatedPoints=function(t){for(var e=1;e<t.length;e++)if(t[e-1].equals(t[e]))return!0;return!1},H.removeRepeatedPoints=function(t){if(!H.hasRepeatedPoints(t))return t;var e=new N(t,!1);return e.toCoordinateArray()},H.reverse=function(t){for(var e=t.length-1,n=Math.trunc(e/2),i=0;n>=i;i++){var r=t[i];t[i]=t[e-i],t[e-i]=r}},H.removeNull=function(t){for(var e=0,n=0;n<t.length;n++)null!==t[n]&&e++;var i=new Array(e).fill(null);if(0===e)return i;for(var r=0,n=0;n<t.length;n++)null!==t[n]&&(i[r++]=t[n]);return i},H.copyDeep=function(){if(1===arguments.length){for(var t=arguments[0],e=new Array(t.length).fill(null),n=0;n<t.length;n++)e[n]=new g(t[n]);return e}if(5===arguments.length)for(var i=arguments[0],r=arguments[1],s=arguments[2],o=arguments[3],a=arguments[4],n=0;a>n;n++)s[o+n]=new g(i[r+n])},H.isEqualReversed=function(t,e){for(var n=0;n<t.length;n++){var i=t[n],r=e[t.length-n-1];if(0!==i.compareTo(r))return!1}return!0},H.envelope=function(t){for(var e=new C,n=0;n<t.length;n++)e.expandToInclude(t[n]);return e},H.toCoordinateArray=function(t){return t.toArray(H.coordArrayType)},H.atLeastNCoordinatesOrNothing=function(t,e){return e.length>=t?e:[]},H.indexOf=function(t,e){for(var n=0;n<e.length;n++)if(t.equals(e[n]))return n;return-1},H.increasingDirection=function(t){for(var e=0;e<Math.trunc(t.length/2);e++){var n=t.length-1-e,i=t[e].compareTo(t[n]);if(0!==i)return i}return 1},H.compare=function(t,e){for(var n=0;n<t.length&&n<e.length;){var i=t[n].compareTo(e[n]);if(0!==i)return i;n++}return n<e.length?-1:n<t.length?1:0},H.minCoordinate=function(t){for(var e=null,n=0;n<t.length;n++)(null===e||e.compareTo(t[n])>0)&&(e=t[n]);return e},H.extract=function(t,e,n){e=T.clamp(e,0,t.length),n=T.clamp(n,-1,t.length);var i=n-e+1;0>n&&(i=0),e>=t.length&&(i=0),e>n&&(i=0);var r=new Array(i).fill(null);if(0===i)return r;for(var s=0,o=e;n>=o;o++)r[s++]=t[o];return r},e(W.prototype,{compare:function(t,e){var n=t,i=e;return H.compare(n,i)},interfaces_:function(){return[a]},getClass:function(){return W}}),e(j.prototype,{compare:function(t,e){var n=t,i=e;if(n.length<i.length)return-1;if(n.length>i.length)return 1;if(0===n.length)return 0;var r=H.compare(n,i),s=H.isEqualReversed(n,i);return s?0:r},OLDcompare:function(t,e){var n=t,i=e;if(n.length<i.length)return-1;if(n.length>i.length)return 1;if(0===n.length)return 0;for(var r=H.increasingDirection(n),s=H.increasingDirection(i),o=r>0?0:n.length-1,a=s>0?0:n.length-1,u=0;u<n.length;u++){var l=n[o].compareTo(i[a]);if(0!==l)return l;o+=r,a+=s}return 0},interfaces_:function(){return[a]},getClass:function(){return j}}),H.ForwardComparator=W,H.BidirectionalComparator=j,H.coordArrayType=new Array(0).fill(null),K.prototype.get=function(){},K.prototype.put=function(){},K.prototype.size=function(){},K.prototype.values=function(){},K.prototype.entrySet=function(){},Z.prototype=new K,Q.prototype=new v,Q.prototype.contains=function(){},J.prototype=new Q,J.prototype.contains=function(t){for(var e=0,n=this.array_.length;n>e;e++){var i=this.array_[e];if(i===t)return!0}return!1},J.prototype.add=function(t){return this.contains(t)?!1:(this.array_.push(t),!0)},J.prototype.addAll=function(t){for(var e=t.iterator();e.hasNext();)this.add(e.next());return!0},J.prototype.remove=function(t){throw new javascript.util.OperationNotSupported},J.prototype.size=function(){return this.array_.length},J.prototype.isEmpty=function(){return 0===this.array_.length},J.prototype.toArray=function(){for(var t=[],e=0,n=this.array_.length;n>e;e++)t.push(this.array_[e]);return t},J.prototype.iterator=function(){return new Js(this)};var Js=function(t){this.hashSet_=t,this.position_=0};Js.prototype.next=function(){if(this.position_===this.hashSet_.size())throw new x;return this.hashSet_.array_[this.position_++]},Js.prototype.hasNext=function(){return this.position_<this.hashSet_.size()},Js.prototype.remove=function(){throw new E};var $s=0,to=1;rt.prototype=new Z,rt.prototype.get=function(t){for(var e=this.root_;null!==e;){var n=t.compareTo(e.key);if(0>n)e=e.left;else{if(!(n>0))return e.value;e=e.right}}return null},rt.prototype.put=function(t,e){if(null===this.root_)return this.root_={key:t,value:e,left:null,right:null,parent:null,color:$s,getValue:function(){return this.value},getKey:function(){return this.key}},this.size_=1,null;var n,i,r=this.root_;do if(n=r,i=t.compareTo(r.key),0>i)r=r.left;else{if(!(i>0)){var s=r.value;return r.value=e,s}r=r.right}while(null!==r);var o={key:t,left:null,right:null,value:e,parent:n,color:$s,getValue:function(){return this.value},getKey:function(){return this.key}};return 0>i?n.left=o:n.right=o,this.fixAfterInsertion(o),this.size_++,null},rt.prototype.fixAfterInsertion=function(t){for(t.color=to;null!=t&&t!=this.root_&&t.parent.color==to;)if(tt(t)==nt(tt(tt(t)))){var e=it(tt(tt(t)));$(e)==to?(et(tt(t),$s),et(e,$s),et(tt(tt(t)),to),t=tt(tt(t))):(t==it(tt(t))&&(t=tt(t),this.rotateLeft(t)),et(tt(t),$s),et(tt(tt(t)),to),this.rotateRight(tt(tt(t))))}else{var e=nt(tt(tt(t)));$(e)==to?(et(tt(t),$s),et(e,$s),et(tt(tt(t)),to),t=tt(tt(t))):(t==nt(tt(t))&&(t=tt(t),this.rotateRight(t)),et(tt(t),$s),et(tt(tt(t)),to),this.rotateLeft(tt(tt(t))))}this.root_.color=$s},rt.prototype.values=function(){var t=new I,e=this.getFirstEntry();if(null!==e)for(t.add(e.value);null!==(e=rt.successor(e));)t.add(e.value);return t},rt.prototype.entrySet=function(){var t=new J,e=this.getFirstEntry();if(null!==e)for(t.add(e);null!==(e=rt.successor(e));)t.add(e);return t},rt.prototype.rotateLeft=function(t){if(null!=t){var e=t.right;t.right=e.left,null!=e.left&&(e.left.parent=t),e.parent=t.parent,null==t.parent?this.root_=e:t.parent.left==t?t.parent.left=e:t.parent.right=e,e.left=t,t.parent=e}},rt.prototype.rotateRight=function(t){if(null!=t){var e=t.left;t.left=e.right,null!=e.right&&(e.right.parent=t),e.parent=t.parent,null==t.parent?this.root_=e:t.parent.right==t?t.parent.right=e:t.parent.left=e,e.right=t,t.parent=e}},rt.prototype.getFirstEntry=function(){var t=this.root_;if(null!=t)for(;null!=t.left;)t=t.left;return t},rt.successor=function(t){if(null===t)return null;if(null!==t.right){for(var e=t.right;null!==e.left;)e=e.left;return e}for(var e=t.parent,n=t;null!==e&&n===e.right;)n=e,e=e.parent;return e},rt.prototype.size=function(){return this.size_},e(st.prototype,{interfaces_:function(){return[]},getClass:function(){return st}}),ot.prototype=new Q,at.prototype=new ot,at.prototype.contains=function(t){for(var e=0,n=this.array_.length;n>e;e++){var i=this.array_[e];if(0===i.compareTo(t))return!0}return!1},at.prototype.add=function(t){if(this.contains(t))return!1;for(var e=0,n=this.array_.length;n>e;e++){var i=this.array_[e];if(1===i.compareTo(t))return this.array_.splice(e,0,t),!0}return this.array_.push(t),!0},at.prototype.addAll=function(t){for(var e=t.iterator();e.hasNext();)this.add(e.next());return!0},at.prototype.remove=function(t){throw new E},at.prototype.size=function(){return this.array_.length},at.prototype.isEmpty=function(){return 0===this.array_.length},at.prototype.toArray=function(){for(var t=[],e=0,n=this.array_.length;n>e;e++)t.push(this.array_[e]);return t},at.prototype.iterator=function(){return new eo(this)};var eo=function(t){this.treeSet_=t,this.position_=0};eo.prototype.next=function(){if(this.position_===this.treeSet_.size())throw new x;return this.treeSet_.array_[this.position_++]},eo.prototype.hasNext=function(){return this.position_<this.treeSet_.size()},eo.prototype.remove=function(){throw new E},ut.sort=function(){var t,e,n,i,r=arguments[0];if(1===arguments.length)return i=function(t,e){return t.compareTo(e)},void r.sort(i);if(2===arguments.length)n=arguments[1],i=function(t,e){return n.compare(t,e)},r.sort(i);else{if(3===arguments.length){e=r.slice(arguments[1],arguments[2]),e.sort();var s=r.slice(0,arguments[1]).concat(e,r.slice(arguments[2],r.length));for(r.splice(0,r.length),t=0;t<s.length;t++)r.push(s[t]);return}if(4===arguments.length){for(e=r.slice(arguments[1],arguments[2]),n=arguments[3],i=function(t,e){return n.compare(t,e)},e.sort(i),s=r.slice(0,arguments[1]).concat(e,r.slice(arguments[2],r.length)),r.splice(0,r.length),t=0;t<s.length;t++)r.push(s[t]);return}}},ut.asList=function(t){for(var e=new I,n=0,i=t.length;i>n;n++)e.add(t[n]);return e},e(lt.prototype,{interfaces_:function(){return[]},getClass:function(){return lt}}),lt.toDimensionSymbol=function(t){switch(t){case lt.FALSE:return lt.SYM_FALSE;case lt.TRUE:return lt.SYM_TRUE;case lt.DONTCARE:return lt.SYM_DONTCARE;case lt.P:return lt.SYM_P;case lt.L:return lt.SYM_L;case lt.A:return lt.SYM_A}throw new i("Unknown dimension value: "+t)},lt.toDimensionValue=function(t){switch(O.toUpperCase(t)){case lt.SYM_FALSE:return lt.FALSE;case lt.SYM_TRUE:return lt.TRUE;case lt.SYM_DONTCARE:return lt.DONTCARE;case lt.SYM_P:return lt.P;case lt.SYM_L:return lt.L;case lt.SYM_A:return lt.A}throw new i("Unknown dimension symbol: "+t)},lt.P=0,lt.L=1,lt.A=2,lt.FALSE=-1,lt.TRUE=-2,lt.DONTCARE=-3,lt.SYM_FALSE="F",lt.SYM_TRUE="T",lt.SYM_DONTCARE="*",lt.SYM_P="0",lt.SYM_L="1",lt.SYM_A="2",e(ht.prototype,{filter:function(t){},interfaces_:function(){return[]},getClass:function(){return ht}}),e(ct.prototype,{filter:function(t,e){},isDone:function(){},isGeometryChanged:function(){},interfaces_:function(){return[]},getClass:function(){return ct}}),h(ft,B),e(ft.prototype,{computeEnvelopeInternal:function(){for(var t=new C,e=0;e<this.geometries.length;e++)t.expandToInclude(this.geometries[e].getEnvelopeInternal());return t},getGeometryN:function(t){return this.geometries[t]},getSortIndex:function(){return B.SORTINDEX_GEOMETRYCOLLECTION},getCoordinates:function(){for(var t=new Array(this.getNumPoints()).fill(null),e=-1,n=0;n<this.geometries.length;n++)for(var i=this.geometries[n].getCoordinates(),r=0;r<i.length;r++)e++,t[e]=i[r];return t},getArea:function(){for(var t=0,e=0;e<this.geometries.length;e++)t+=this.geometries[e].getArea();return t},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(!this.isEquivalentClass(t))return!1;var n=t;if(this.geometries.length!==n.geometries.length)return!1;for(var i=0;i<this.geometries.length;i++)if(!this.geometries[i].equalsExact(n.geometries[i],e))return!1;return!0}return B.prototype.equalsExact.apply(this,arguments)},normalize:function(){for(var t=0;t<this.geometries.length;t++)this.geometries[t].normalize();ut.sort(this.geometries)},getCoordinate:function(){return this.isEmpty()?null:this.geometries[0].getCoordinate()},getBoundaryDimension:function(){for(var t=lt.FALSE,e=0;e<this.geometries.length;e++)t=Math.max(t,this.geometries[e].getBoundaryDimension());return t},getDimension:function(){for(var t=lt.FALSE,e=0;e<this.geometries.length;e++)t=Math.max(t,this.geometries[e].getDimension());return t},getLength:function(){for(var t=0,e=0;e<this.geometries.length;e++)t+=this.geometries[e].getLength();return t},getNumPoints:function(){for(var t=0,e=0;e<this.geometries.length;e++)t+=this.geometries[e].getNumPoints();return t},getNumGeometries:function(){return this.geometries.length},reverse:function(){for(var t=this.geometries.length,e=new Array(t).fill(null),n=0;n<this.geometries.length;n++)e[n]=this.geometries[n].reverse();return this.getFactory().createGeometryCollection(e)},compareToSameClass:function(){if(1===arguments.length){var t=arguments[0],e=new at(ut.asList(this.geometries)),n=new at(ut.asList(t.geometries));return this.compare(e,n)}if(2===arguments.length){for(var i=arguments[0],r=arguments[1],s=i,o=this.getNumGeometries(),a=s.getNumGeometries(),u=0;o>u&&a>u;){var l=this.getGeometryN(u),h=s.getGeometryN(u),c=l.compareToSameClass(h,r);if(0!==c)return c;u++}return o>u?1:a>u?-1:0}},apply:function(){if(R(arguments[0],z))for(var t=arguments[0],e=0;e<this.geometries.length;e++)this.geometries[e].apply(t);else if(R(arguments[0],ct)){var n=arguments[0];if(0===this.geometries.length)return null;for(var e=0;e<this.geometries.length&&(this.geometries[e].apply(n),!n.isDone());e++);n.isGeometryChanged()&&this.geometryChanged()}else if(R(arguments[0],ht)){var i=arguments[0];i.filter(this);for(var e=0;e<this.geometries.length;e++)this.geometries[e].apply(i)}else if(R(arguments[0],q)){var r=arguments[0];r.filter(this);for(var e=0;e<this.geometries.length;e++)this.geometries[e].apply(r)}},getBoundary:function(){return this.checkNotGeometryCollection(this),f.shouldNeverReachHere(),null},clone:function(){var t=B.prototype.clone.call(this);t.geometries=new Array(this.geometries.length).fill(null);for(var e=0;e<this.geometries.length;e++)t.geometries[e]=this.geometries[e].clone();return t},getGeometryType:function(){return"GeometryCollection"},copy:function(){for(var t=new Array(this.geometries.length).fill(null),e=0;e<t.length;e++)t[e]=this.geometries[e].copy();return new ft(t,this.factory)},isEmpty:function(){for(var t=0;t<this.geometries.length;t++)if(!this.geometries[t].isEmpty())return!1;return!0},interfaces_:function(){return[]},getClass:function(){return ft}}),ft.serialVersionUID=-0x4f07bcb1f857d800,h(gt,ft),e(gt.prototype,{getSortIndex:function(){return B.SORTINDEX_MULTILINESTRING},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.isEquivalentClass(t)?ft.prototype.equalsExact.call(this,t,e):!1}return ft.prototype.equalsExact.apply(this,arguments)},getBoundaryDimension:function(){return this.isClosed()?lt.FALSE:0},isClosed:function(){if(this.isEmpty())return!1;for(var t=0;t<this.geometries.length;t++)if(!this.geometries[t].isClosed())return!1;return!0},getDimension:function(){return 1},reverse:function(){for(var t=this.geometries.length,e=new Array(t).fill(null),n=0;n<this.geometries.length;n++)e[t-1-n]=this.geometries[n].reverse();return this.getFactory().createMultiLineString(e)},getBoundary:function(){return new dt(this).getBoundary()},getGeometryType:function(){return"MultiLineString"},copy:function(){for(var t=new Array(this.geometries.length).fill(null),e=0;e<t.length;e++)t[e]=this.geometries[e].copy();return new gt(t,this.factory)},interfaces_:function(){
return[st]},getClass:function(){return gt}}),gt.serialVersionUID=0x7155d2ab4afa8000,e(dt.prototype,{boundaryMultiLineString:function(t){if(this.geom.isEmpty())return this.getEmptyMultiPoint();var e=this.computeBoundaryCoordinates(t);return 1===e.length?this.geomFact.createPoint(e[0]):this.geomFact.createMultiPointFromCoords(e)},getBoundary:function(){return this.geom instanceof St?this.boundaryLineString(this.geom):this.geom instanceof gt?this.boundaryMultiLineString(this.geom):this.geom.getBoundary()},boundaryLineString:function(t){if(this.geom.isEmpty())return this.getEmptyMultiPoint();if(t.isClosed()){var e=this.bnRule.isInBoundary(2);return e?t.getStartPoint():this.geomFact.createMultiPoint()}return this.geomFact.createMultiPoint([t.getStartPoint(),t.getEndPoint()])},getEmptyMultiPoint:function(){return this.geomFact.createMultiPoint()},computeBoundaryCoordinates:function(t){var e=new I;this.endpointMap=new rt;for(var n=0;n<t.getNumGeometries();n++){var i=t.getGeometryN(n);0!==i.getNumPoints()&&(this.addEndpoint(i.getCoordinateN(0)),this.addEndpoint(i.getCoordinateN(i.getNumPoints()-1)))}for(var r=this.endpointMap.entrySet().iterator();r.hasNext();){var s=r.next(),o=s.getValue(),a=o.count;this.bnRule.isInBoundary(a)&&e.add(s.getKey())}return H.toCoordinateArray(e)},addEndpoint:function(t){var e=this.endpointMap.get(t);null===e&&(e=new pt,this.endpointMap.put(t,e)),e.count++},interfaces_:function(){return[]},getClass:function(){return dt}}),dt.getBoundary=function(){if(1===arguments.length){var t=arguments[0],e=new dt(t);return e.getBoundary()}if(2===arguments.length){var n=arguments[0],i=arguments[1],e=new dt(n,i);return e.getBoundary()}},e(pt.prototype,{interfaces_:function(){return[]},getClass:function(){return pt}}),e(Nt.prototype,{interfaces_:function(){return[]},getClass:function(){return Nt}}),Nt.chars=function(t,e){for(var n=new Array(e).fill(null),i=0;e>i;i++)n[i]=t;return new String(n)},Nt.getStackTrace=function(){if(1===arguments.length){var t=arguments[0],e=new xt,n=new vt(e);return t.printStackTrace(n),e.toString()}if(2===arguments.length){for(var i=arguments[0],r=arguments[1],s="",o=new mt(Nt.getStackTrace(i)),a=new It(o),u=0;r>u;u++)try{s+=a.readLine()+Nt.NEWLINE}catch(t){if(!(t instanceof Et))throw t;f.shouldNeverReachHere()}finally{}return s}},Nt.split=function(t,e){for(var n=e.length,i=new I,r=""+t,s=r.indexOf(e);s>=0;){var o=r.substring(0,s);i.add(o),r=r.substring(s+n),s=r.indexOf(e)}r.length>0&&i.add(r);for(var a=new Array(i.size()).fill(null),u=0;u<a.length;u++)a[u]=i.get(u);return a},Nt.toString=function(){if(1===arguments.length){var t=arguments[0];return Nt.SIMPLE_ORDINATE_FORMAT.format(t)}},Nt.spaces=function(t){return Nt.chars(" ",t)},Nt.NEWLINE=A.getProperty("line.separator"),Nt.SIMPLE_ORDINATE_FORMAT=new yt("0.#"),e(Ct.prototype,{interfaces_:function(){return[]},getClass:function(){return Ct}}),Ct.copyCoord=function(t,e,n,i){for(var r=Math.min(t.getDimension(),n.getDimension()),s=0;r>s;s++)n.setOrdinate(i,s,t.getOrdinate(e,s))},Ct.isRing=function(t){var e=t.size();return 0===e?!0:3>=e?!1:t.getOrdinate(0,D.X)===t.getOrdinate(e-1,D.X)&&t.getOrdinate(0,D.Y)===t.getOrdinate(e-1,D.Y)},Ct.isEqual=function(t,e){var n=t.size(),i=e.size();if(n!==i)return!1;for(var s=Math.min(t.getDimension(),e.getDimension()),o=0;n>o;o++)for(var a=0;s>a;a++){var u=t.getOrdinate(o,a),l=e.getOrdinate(o,a);if(!(t.getOrdinate(o,a)===e.getOrdinate(o,a)||r.isNaN(u)&&r.isNaN(l)))return!1}return!0},Ct.extend=function(t,e,n){var i=t.create(n,e.getDimension()),r=e.size();if(Ct.copy(e,0,i,0,r),r>0)for(var s=r;n>s;s++)Ct.copy(e,r-1,i,s,1);return i},Ct.reverse=function(t){for(var e=t.size()-1,n=Math.trunc(e/2),i=0;n>=i;i++)Ct.swap(t,i,e-i)},Ct.swap=function(t,e,n){if(e===n)return null;for(var i=0;i<t.getDimension();i++){var r=t.getOrdinate(e,i);t.setOrdinate(e,i,t.getOrdinate(n,i)),t.setOrdinate(n,i,r)}},Ct.copy=function(t,e,n,i,r){for(var s=0;r>s;s++)Ct.copyCoord(t,e+s,n,i+s)},Ct.toString=function(){if(1===arguments.length){var t=arguments[0],e=t.size();if(0===e)return"()";var n=t.getDimension(),i=new P;i.append("(");for(var r=0;e>r;r++){r>0&&i.append(" ");for(var s=0;n>s;s++)s>0&&i.append(","),i.append(Nt.toString(t.getOrdinate(r,s)))}return i.append(")"),i.toString()}},Ct.ensureValidRing=function(t,e){var n=e.size();if(0===n)return e;if(3>=n)return Ct.createClosedRing(t,e,4);var i=e.getOrdinate(0,D.X)===e.getOrdinate(n-1,D.X)&&e.getOrdinate(0,D.Y)===e.getOrdinate(n-1,D.Y);return i?e:Ct.createClosedRing(t,e,n+1)},Ct.createClosedRing=function(t,e,n){var i=t.create(n,e.getDimension()),r=e.size();Ct.copy(e,0,i,0,r);for(var s=r;n>s;s++)Ct.copy(e,0,i,s,1);return i},h(St,B),e(St.prototype,{computeEnvelopeInternal:function(){return this.isEmpty()?new C:this.points.expandEnvelope(new C)},isRing:function(){return this.isClosed()&&this.isSimple()},getSortIndex:function(){return B.SORTINDEX_LINESTRING},getCoordinates:function(){return this.points.toCoordinateArray()},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(!this.isEquivalentClass(t))return!1;var n=t;if(this.points.size()!==n.points.size())return!1;for(var i=0;i<this.points.size();i++)if(!this.equal(this.points.getCoordinate(i),n.points.getCoordinate(i),e))return!1;return!0}return B.prototype.equalsExact.apply(this,arguments)},normalize:function(){for(var t=0;t<Math.trunc(this.points.size()/2);t++){var e=this.points.size()-1-t;if(!this.points.getCoordinate(t).equals(this.points.getCoordinate(e)))return this.points.getCoordinate(t).compareTo(this.points.getCoordinate(e))>0&&Ct.reverse(this.points),null}},getCoordinate:function(){return this.isEmpty()?null:this.points.getCoordinate(0)},getBoundaryDimension:function(){return this.isClosed()?lt.FALSE:0},isClosed:function(){return this.isEmpty()?!1:this.getCoordinateN(0).equals2D(this.getCoordinateN(this.getNumPoints()-1))},getEndPoint:function(){return this.isEmpty()?null:this.getPointN(this.getNumPoints()-1)},getDimension:function(){return 1},getLength:function(){return he.computeLength(this.points)},getNumPoints:function(){return this.points.size()},reverse:function(){var t=this.points.copy();Ct.reverse(t);var e=this.getFactory().createLineString(t);return e},compareToSameClass:function(){if(1===arguments.length){for(var t=arguments[0],e=t,n=0,i=0;n<this.points.size()&&i<e.points.size();){var r=this.points.getCoordinate(n).compareTo(e.points.getCoordinate(i));if(0!==r)return r;n++,i++}return n<this.points.size()?1:i<e.points.size()?-1:0}if(2===arguments.length){var s=arguments[0],o=arguments[1],e=s;return o.compare(this.points,e.points)}},apply:function(){if(R(arguments[0],z))for(var t=arguments[0],e=0;e<this.points.size();e++)t.filter(this.points.getCoordinate(e));else if(R(arguments[0],ct)){var n=arguments[0];if(0===this.points.size())return null;for(var e=0;e<this.points.size()&&(n.filter(this.points,e),!n.isDone());e++);n.isGeometryChanged()&&this.geometryChanged()}else if(R(arguments[0],ht)){var i=arguments[0];i.filter(this)}else if(R(arguments[0],q)){var r=arguments[0];r.filter(this)}},getBoundary:function(){return new dt(this).getBoundary()},isEquivalentClass:function(t){return t instanceof St},clone:function(){var t=B.prototype.clone.call(this);return t.points=this.points.clone(),t},getCoordinateN:function(t){return this.points.getCoordinate(t)},getGeometryType:function(){return"LineString"},copy:function(){return new St(this.points.copy(),this.factory)},getCoordinateSequence:function(){return this.points},isEmpty:function(){return 0===this.points.size()},init:function(t){if(null===t&&(t=this.getFactory().getCoordinateSequenceFactory().create([])),1===t.size())throw new i("Invalid number of points in LineString (found "+t.size()+" - must be 0 or >= 2)");this.points=t},isCoordinate:function(t){for(var e=0;e<this.points.size();e++)if(this.points.getCoordinate(e).equals(t))return!0;return!1},getStartPoint:function(){return this.isEmpty()?null:this.getPointN(0)},getPointN:function(t){return this.getFactory().createPoint(this.points.getCoordinate(t))},interfaces_:function(){return[st]},getClass:function(){return St}}),St.serialVersionUID=0x2b2b51ba435c8e00,e(wt.prototype,{interfaces_:function(){return[]},getClass:function(){return wt}}),h(Lt,B),e(Lt.prototype,{computeEnvelopeInternal:function(){if(this.isEmpty())return new C;var t=new C;return t.expandToInclude(this.coordinates.getX(0),this.coordinates.getY(0)),t},getSortIndex:function(){return B.SORTINDEX_POINT},getCoordinates:function(){return this.isEmpty()?[]:[this.getCoordinate()]},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.isEquivalentClass(t)?this.isEmpty()&&t.isEmpty()?!0:this.isEmpty()!==t.isEmpty()?!1:this.equal(t.getCoordinate(),this.getCoordinate(),e):!1}return B.prototype.equalsExact.apply(this,arguments)},normalize:function(){},getCoordinate:function(){return 0!==this.coordinates.size()?this.coordinates.getCoordinate(0):null},getBoundaryDimension:function(){return lt.FALSE},getDimension:function(){return 0},getNumPoints:function(){return this.isEmpty()?0:1},reverse:function(){return this.copy()},getX:function(){if(null===this.getCoordinate())throw new IllegalStateException("getX called on empty Point");return this.getCoordinate().x},compareToSameClass:function(){if(1===arguments.length){var t=arguments[0],e=t;return this.getCoordinate().compareTo(e.getCoordinate())}if(2===arguments.length){var n=arguments[0],i=arguments[1],e=n;return i.compare(this.coordinates,e.coordinates)}},apply:function(){if(R(arguments[0],z)){var t=arguments[0];if(this.isEmpty())return null;t.filter(this.getCoordinate())}else if(R(arguments[0],ct)){var e=arguments[0];if(this.isEmpty())return null;e.filter(this.coordinates,0),e.isGeometryChanged()&&this.geometryChanged()}else if(R(arguments[0],ht)){var n=arguments[0];n.filter(this)}else if(R(arguments[0],q)){var i=arguments[0];i.filter(this)}},getBoundary:function(){return this.getFactory().createGeometryCollection(null)},clone:function(){var t=B.prototype.clone.call(this);return t.coordinates=this.coordinates.clone(),t},getGeometryType:function(){return"Point"},copy:function(){return new Lt(this.coordinates.copy(),this.factory)},getCoordinateSequence:function(){return this.coordinates},getY:function(){if(null===this.getCoordinate())throw new IllegalStateException("getY called on empty Point");return this.getCoordinate().y},isEmpty:function(){return 0===this.coordinates.size()},init:function(t){null===t&&(t=this.getFactory().getCoordinateSequenceFactory().create([])),f.isTrue(t.size()<=1),this.coordinates=t},isSimple:function(){return!0},interfaces_:function(){return[wt]},getClass:function(){return Lt}}),Lt.serialVersionUID=0x44077bad161cbc00,e(Rt.prototype,{interfaces_:function(){return[]},getClass:function(){return Rt}}),h(Tt,B),e(Tt.prototype,{computeEnvelopeInternal:function(){return this.shell.getEnvelopeInternal()},getSortIndex:function(){return B.SORTINDEX_POLYGON},getCoordinates:function(){if(this.isEmpty())return[];for(var t=new Array(this.getNumPoints()).fill(null),e=-1,n=this.shell.getCoordinates(),i=0;i<n.length;i++)e++,t[e]=n[i];for(var r=0;r<this.holes.length;r++)for(var s=this.holes[r].getCoordinates(),o=0;o<s.length;o++)e++,t[e]=s[o];return t},getArea:function(){var t=0;t+=Math.abs(he.signedArea(this.shell.getCoordinateSequence()));for(var e=0;e<this.holes.length;e++)t-=Math.abs(he.signedArea(this.holes[e].getCoordinateSequence()));return t},isRectangle:function(){if(0!==this.getNumInteriorRing())return!1;if(null===this.shell)return!1;if(5!==this.shell.getNumPoints())return!1;for(var t=this.shell.getCoordinateSequence(),e=this.getEnvelopeInternal(),n=0;5>n;n++){var i=t.getX(n);if(i!==e.getMinX()&&i!==e.getMaxX())return!1;var r=t.getY(n);if(r!==e.getMinY()&&r!==e.getMaxY())return!1}for(var s=t.getX(0),o=t.getY(0),n=1;4>=n;n++){var i=t.getX(n),r=t.getY(n),a=i!==s,u=r!==o;if(a===u)return!1;s=i,o=r}return!0},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(!this.isEquivalentClass(t))return!1;var n=t,i=this.shell,r=n.shell;if(!i.equalsExact(r,e))return!1;if(this.holes.length!==n.holes.length)return!1;for(var s=0;s<this.holes.length;s++)if(!this.holes[s].equalsExact(n.holes[s],e))return!1;return!0}return B.prototype.equalsExact.apply(this,arguments)},normalize:function(){if(0===arguments.length){this.normalize(this.shell,!0);for(var t=0;t<this.holes.length;t++)this.normalize(this.holes[t],!1);ut.sort(this.holes)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(e.isEmpty())return null;var i=new Array(e.getCoordinates().length-1).fill(null);A.arraycopy(e.getCoordinates(),0,i,0,i.length);var r=H.minCoordinate(e.getCoordinates());H.scroll(i,r),A.arraycopy(i,0,e.getCoordinates(),0,i.length),e.getCoordinates()[i.length]=i[0],he.isCCW(e.getCoordinates())===n&&H.reverse(e.getCoordinates())}},getCoordinate:function(){return this.shell.getCoordinate()},getNumInteriorRing:function(){return this.holes.length},getBoundaryDimension:function(){return 1},getDimension:function(){return 2},getLength:function(){var t=0;t+=this.shell.getLength();for(var e=0;e<this.holes.length;e++)t+=this.holes[e].getLength();return t},getNumPoints:function(){for(var t=this.shell.getNumPoints(),e=0;e<this.holes.length;e++)t+=this.holes[e].getNumPoints();return t},reverse:function(){var t=this.copy();t.shell=this.shell.copy().reverse(),t.holes=new Array(this.holes.length).fill(null);for(var e=0;e<this.holes.length;e++)t.holes[e]=this.holes[e].copy().reverse();return t},convexHull:function(){return this.getExteriorRing().convexHull()},compareToSameClass:function(){if(1===arguments.length){var t=arguments[0],e=this.shell,n=t.shell;return e.compareToSameClass(n)}if(2===arguments.length){var i=arguments[0],r=arguments[1],s=i,e=this.shell,n=s.shell,o=e.compareToSameClass(n,r);if(0!==o)return o;for(var a=this.getNumInteriorRing(),u=s.getNumInteriorRing(),l=0;a>l&&u>l;){var h=this.getInteriorRingN(l),c=s.getInteriorRingN(l),f=h.compareToSameClass(c,r);if(0!==f)return f;l++}return a>l?1:u>l?-1:0}},apply:function(){if(R(arguments[0],z)){var t=arguments[0];this.shell.apply(t);for(var e=0;e<this.holes.length;e++)this.holes[e].apply(t)}else if(R(arguments[0],ct)){var n=arguments[0];if(this.shell.apply(n),!n.isDone())for(var e=0;e<this.holes.length&&(this.holes[e].apply(n),!n.isDone());e++);n.isGeometryChanged()&&this.geometryChanged()}else if(R(arguments[0],ht)){var i=arguments[0];i.filter(this)}else if(R(arguments[0],q)){var r=arguments[0];r.filter(this),this.shell.apply(r);for(var e=0;e<this.holes.length;e++)this.holes[e].apply(r)}},getBoundary:function(){if(this.isEmpty())return this.getFactory().createMultiLineString();var t=new Array(this.holes.length+1).fill(null);t[0]=this.shell;for(var e=0;e<this.holes.length;e++)t[e+1]=this.holes[e];return t.length<=1?this.getFactory().createLinearRing(t[0].getCoordinateSequence()):this.getFactory().createMultiLineString(t)},clone:function(){var t=B.prototype.clone.call(this);t.shell=this.shell.clone(),t.holes=new Array(this.holes.length).fill(null);for(var e=0;e<this.holes.length;e++)t.holes[e]=this.holes[e].clone();return t},getGeometryType:function(){return"Polygon"},copy:function(){for(var t=this.shell.copy(),e=new Array(this.holes.length).fill(null),n=0;n<e.length;n++)e[n]=this.holes[n].copy();return new Tt(t,e,this.factory)},getExteriorRing:function(){return this.shell},isEmpty:function(){return this.shell.isEmpty()},getInteriorRingN:function(t){return this.holes[t]},interfaces_:function(){return[Rt]},getClass:function(){return Tt}}),Tt.serialVersionUID=-0x307ffefd8dc97200,h(Pt,ft),e(Pt.prototype,{getSortIndex:function(){return B.SORTINDEX_MULTIPOINT},isValid:function(){return!0},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.isEquivalentClass(t)?ft.prototype.equalsExact.call(this,t,e):!1}return ft.prototype.equalsExact.apply(this,arguments)},getCoordinate:function(){if(1===arguments.length){var t=arguments[0];return this.geometries[t].getCoordinate()}return ft.prototype.getCoordinate.apply(this,arguments)},getBoundaryDimension:function(){return lt.FALSE},getDimension:function(){return 0},getBoundary:function(){return this.getFactory().createGeometryCollection(null)},getGeometryType:function(){return"MultiPoint"},copy:function(){for(var t=new Array(this.geometries.length).fill(null),e=0;e<t.length;e++)t[e]=this.geometries[e].copy();return new Pt(t,this.factory)},interfaces_:function(){return[wt]},getClass:function(){return Pt}}),Pt.serialVersionUID=-0x6fb1ed4162e0fc00,h(bt,St),e(bt.prototype,{getSortIndex:function(){return B.SORTINDEX_LINEARRING},getBoundaryDimension:function(){return lt.FALSE},isClosed:function(){return this.isEmpty()?!0:St.prototype.isClosed.call(this)},reverse:function(){var t=this.points.copy();Ct.reverse(t);var e=this.getFactory().createLinearRing(t);return e},validateConstruction:function(){if(!this.isEmpty()&&!St.prototype.isClosed.call(this))throw new i("Points of LinearRing do not form a closed linestring");if(this.getCoordinateSequence().size()>=1&&this.getCoordinateSequence().size()<bt.MINIMUM_VALID_SIZE)throw new i("Invalid number of points in LinearRing (found "+this.getCoordinateSequence().size()+" - must be 0 or >= 4)")},getGeometryType:function(){return"LinearRing"},copy:function(){return new bt(this.points.copy(),this.factory)},interfaces_:function(){return[]},getClass:function(){return bt}}),bt.MINIMUM_VALID_SIZE=4,bt.serialVersionUID=-0x3b229e262367a600,h(Ot,ft),e(Ot.prototype,{getSortIndex:function(){return B.SORTINDEX_MULTIPOLYGON},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.isEquivalentClass(t)?ft.prototype.equalsExact.call(this,t,e):!1}return ft.prototype.equalsExact.apply(this,arguments)},getBoundaryDimension:function(){return 1},getDimension:function(){return 2},reverse:function(){for(var t=this.geometries.length,e=new Array(t).fill(null),n=0;n<this.geometries.length;n++)e[n]=this.geometries[n].reverse();return this.getFactory().createMultiPolygon(e)},getBoundary:function(){if(this.isEmpty())return this.getFactory().createMultiLineString();for(var t=new I,e=0;e<this.geometries.length;e++)for(var n=this.geometries[e],i=n.getBoundary(),r=0;r<i.getNumGeometries();r++)t.add(i.getGeometryN(r));var s=new Array(t.size()).fill(null);return this.getFactory().createMultiLineString(t.toArray(s))},getGeometryType:function(){return"MultiPolygon"},copy:function(){for(var t=new Array(this.geometries.length).fill(null),e=0;e<t.length;e++)t[e]=this.geometries[e].copy();return new Ot(t,this.factory)},interfaces_:function(){return[Rt]},getClass:function(){return Ot}}),Ot.serialVersionUID=-0x7a5aa1369171980,e(_t.prototype,{setCopyUserData:function(t){this.isUserDataCopied=t},edit:function(t,e){if(null===t)return null;var n=this.editInternal(t,e);return this.isUserDataCopied&&n.setUserData(t.getUserData()),n},editInternal:function(t,e){return null===this.factory&&(this.factory=t.getFactory()),t instanceof ft?this.editGeometryCollection(t,e):t instanceof Tt?this.editPolygon(t,e):t instanceof Lt?e.edit(t,this.factory):t instanceof St?e.edit(t,this.factory):(f.shouldNeverReachHere("Unsupported Geometry class: "+t.getClass().getName()),null)},editGeometryCollection:function(t,e){for(var n=e.edit(t,this.factory),i=new I,r=0;r<n.getNumGeometries();r++){var s=this.edit(n.getGeometryN(r),e);null===s||s.isEmpty()||i.add(s)}return n.getClass()===Pt?this.factory.createMultiPoint(i.toArray([])):n.getClass()===gt?this.factory.createMultiLineString(i.toArray([])):n.getClass()===Ot?this.factory.createMultiPolygon(i.toArray([])):this.factory.createGeometryCollection(i.toArray([]))},editPolygon:function(t,e){var n=e.edit(t,this.factory);if(null===n&&(n=this.factory.createPolygon(null)),n.isEmpty())return n;var i=this.edit(n.getExteriorRing(),e);if(null===i||i.isEmpty())return this.factory.createPolygon();for(var r=new I,s=0;s<n.getNumInteriorRing();s++){var o=this.edit(n.getInteriorRingN(s),e);null===o||o.isEmpty()||r.add(o)}return this.factory.createPolygon(i,r.toArray([]))},interfaces_:function(){return[]},getClass:function(){return _t}}),_t.GeometryEditorOperation=Mt,e(Dt.prototype,{edit:function(t,e){return t},interfaces_:function(){return[Mt]},getClass:function(){return Dt}}),e(At.prototype,{edit:function(t,e){if(t instanceof bt)return e.createLinearRing(this.editCoordinates(t.getCoordinates(),t));if(t instanceof St)return e.createLineString(this.editCoordinates(t.getCoordinates(),t));if(t instanceof Lt){var n=this.editCoordinates(t.getCoordinates(),t);return n.length>0?e.createPoint(n[0]):e.createPoint()}return t},interfaces_:function(){return[Mt]},getClass:function(){return At}}),e(Ft.prototype,{edit:function(t,e){return t instanceof bt?e.createLinearRing(this.edit(t.getCoordinateSequence(),t)):t instanceof St?e.createLineString(this.edit(t.getCoordinateSequence(),t)):t instanceof Lt?e.createPoint(this.edit(t.getCoordinateSequence(),t)):t},interfaces_:function(){return[Mt]},getClass:function(){return Ft}}),_t.NoOpGeometryOperation=Dt,_t.CoordinateOperation=At,_t.CoordinateSequenceOperation=Ft,e(Gt.prototype,{setOrdinate:function(t,e,n){switch(e){case D.X:this.coordinates[t].x=n;break;case D.Y:this.coordinates[t].y=n;break;case D.Z:this.coordinates[t].z=n;break;default:throw new i("invalid ordinateIndex")}},size:function(){return this.coordinates.length},getOrdinate:function(t,e){switch(e){case D.X:return this.coordinates[t].x;case D.Y:return this.coordinates[t].y;case D.Z:return this.coordinates[t].z}return r.NaN},getCoordinate:function(){if(1===arguments.length){var t=arguments[0];return this.coordinates[t]}if(2===arguments.length){var e=arguments[0],n=arguments[1];n.x=this.coordinates[e].x,n.y=this.coordinates[e].y,n.z=this.coordinates[e].z}},getCoordinateCopy:function(t){return new g(this.coordinates[t])},getDimension:function(){return this.dimension},getX:function(t){return this.coordinates[t].x},clone:function(){for(var t=new Array(this.size()).fill(null),e=0;e<this.coordinates.length;e++)t[e]=this.coordinates[e].clone();return new Gt(t,this.dimension)},expandEnvelope:function(t){for(var e=0;e<this.coordinates.length;e++)t.expandToInclude(this.coordinates[e]);return t},copy:function(){for(var t=new Array(this.size()).fill(null),e=0;e<this.coordinates.length;e++)t[e]=this.coordinates[e].copy();return new Gt(t,this.dimension)},toString:function(){if(this.coordinates.length>0){var t=new P(17*this.coordinates.length);t.append("("),t.append(this.coordinates[0]);for(var e=1;e<this.coordinates.length;e++)t.append(", "),t.append(this.coordinates[e]);return t.append(")"),t.toString()}return"()"},getY:function(t){return this.coordinates[t].y},toCoordinateArray:function(){return this.coordinates},interfaces_:function(){return[D,u]},getClass:function(){return Gt}}),Gt.serialVersionUID=-0xcb44a778db18e00,e(qt.prototype,{readResolve:function(){return qt.instance()},create:function(){if(1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];return new Gt(t)}if(R(arguments[0],D)){var e=arguments[0];return new Gt(e)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];return i>3&&(i=3),2>i?new Gt(n):new Gt(n,i)}},interfaces_:function(){return[G,u]},getClass:function(){return qt}}),qt.instance=function(){return qt.instanceObject},qt.serialVersionUID=-0x38e49fa6cf6f2e00,qt.instanceObject=new qt;var no,io=Object.defineProperty,ro=zt({delete:kt,has:Xt,get:Yt,set:Ht,keys:jt,values:Kt,entries:Zt,forEach:$t,clear:Wt}),so="undefined"!=typeof Map&&Map.prototype.values?Map:ro;te.prototype=new K,te.prototype.get=function(t){return this.map_.get(t)||null},te.prototype.put=function(t,e){return this.map_.set(t,e),e},te.prototype.values=function(){for(var t=new I,e=this.map_.values(),n=e.next();!n.done;)t.add(n.value),n=e.next();return t},te.prototype.entrySet=function(){var t=new J;return this.map_.entries().forEach(function(e){return t.add(e)}),t},te.prototype.size=function(){return this.map_.size()},e(ee.prototype,{equals:function(t){if(!(t instanceof ee))return!1;var e=t;return this.modelType===e.modelType&&this.scale===e.scale},compareTo:function(t){var e=t,n=this.getMaximumSignificantDigits(),i=e.getMaximumSignificantDigits();return new b(n).compareTo(new b(i))},getScale:function(){return this.scale},isFloating:function(){return this.modelType===ee.FLOATING||this.modelType===ee.FLOATING_SINGLE},getType:function(){return this.modelType},toString:function(){var t="UNKNOWN";return this.modelType===ee.FLOATING?t="Floating":this.modelType===ee.FLOATING_SINGLE?t="Floating-Single":this.modelType===ee.FIXED&&(t="Fixed (Scale="+this.getScale()+")"),t},makePrecise:function(){if("number"==typeof arguments[0]){var t=arguments[0];if(r.isNaN(t))return t;if(this.modelType===ee.FLOATING_SINGLE){var e=t;return e}return this.modelType===ee.FIXED?Math.round(t*this.scale)/this.scale:t}if(arguments[0]instanceof g){var n=arguments[0];if(this.modelType===ee.FLOATING)return null;n.x=this.makePrecise(n.x),n.y=this.makePrecise(n.y)}},getMaximumSignificantDigits:function(){var t=16;return this.modelType===ee.FLOATING?t=16:this.modelType===ee.FLOATING_SINGLE?t=6:this.modelType===ee.FIXED&&(t=1+Math.trunc(Math.ceil(Math.log(this.getScale())/Math.log(10)))),t},setScale:function(t){this.scale=Math.abs(t)},interfaces_:function(){return[u,s]},getClass:function(){return ee}}),ee.mostPrecise=function(t,e){return t.compareTo(e)>=0?t:e},e(ne.prototype,{readResolve:function(){return ne.nameToTypeMap.get(this.name)},toString:function(){return this.name},interfaces_:function(){return[u]},getClass:function(){return ne}}),ne.serialVersionUID=-552860263173159e4,ne.nameToTypeMap=new te,ee.Type=ne,ee.serialVersionUID=0x6bee6404e9a25c00,ee.FIXED=new ne("FIXED"),ee.FLOATING=new ne("FLOATING"),ee.FLOATING_SINGLE=new ne("FLOATING SINGLE"),ee.maximumPreciseValue=9007199254740992,e(ie.prototype,{toGeometry:function(t){return t.isNull()?this.createPoint(null):t.getMinX()===t.getMaxX()&&t.getMinY()===t.getMaxY()?this.createPoint(new g(t.getMinX(),t.getMinY())):t.getMinX()===t.getMaxX()||t.getMinY()===t.getMaxY()?this.createLineString([new g(t.getMinX(),t.getMinY()),new g(t.getMaxX(),t.getMaxY())]):this.createPolygon(this.createLinearRing([new g(t.getMinX(),t.getMinY()),new g(t.getMinX(),t.getMaxY()),new g(t.getMaxX(),t.getMaxY()),new g(t.getMaxX(),t.getMinY()),new g(t.getMinX(),t.getMinY())]),null)},createLineString:function(){if(0===arguments.length)return this.createLineString(this.getCoordinateSequenceFactory().create([]));if(1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];return this.createLineString(null!==t?this.getCoordinateSequenceFactory().create(t):null)}if(R(arguments[0],D)){var e=arguments[0];return new St(e,this)}}},createMultiLineString:function(){if(0===arguments.length)return new gt(null,this);if(1===arguments.length){var t=arguments[0];return new gt(t,this)}},buildGeometry:function(t){for(var e=null,n=!1,i=!1,r=t.iterator();r.hasNext();){var s=r.next(),o=s.getClass();null===e&&(e=o),o!==e&&(n=!0),s.isGeometryCollectionOrDerived()&&(i=!0)}if(null===e)return this.createGeometryCollection();if(n||i)return this.createGeometryCollection(ie.toGeometryArray(t));var a=t.iterator().next(),u=t.size()>1;if(u){if(a instanceof Tt)return this.createMultiPolygon(ie.toPolygonArray(t));if(a instanceof St)return this.createMultiLineString(ie.toLineStringArray(t));if(a instanceof Lt)return this.createMultiPoint(ie.toPointArray(t));f.shouldNeverReachHere("Unhandled class: "+a.getClass().getName())}return a},createMultiPointFromCoords:function(t){return this.createMultiPoint(null!==t?this.getCoordinateSequenceFactory().create(t):null)},createPoint:function(){if(0===arguments.length)return this.createPoint(this.getCoordinateSequenceFactory().create([]));if(1===arguments.length){if(arguments[0]instanceof g){var t=arguments[0];return this.createPoint(null!==t?this.getCoordinateSequenceFactory().create([t]):null)}if(R(arguments[0],D)){var e=arguments[0];return new Lt(e,this)}}},getCoordinateSequenceFactory:function(){return this.coordinateSequenceFactory},createPolygon:function(){if(0===arguments.length)return new Tt(null,null,this);if(1===arguments.length){if(R(arguments[0],D)){var t=arguments[0];return this.createPolygon(this.createLinearRing(t))}if(arguments[0]instanceof Array){var e=arguments[0];return this.createPolygon(this.createLinearRing(e))}if(arguments[0]instanceof bt){var n=arguments[0];return this.createPolygon(n,null)}}else if(2===arguments.length){var i=arguments[0],r=arguments[1];return new Tt(i,r,this)}},getSRID:function(){return this.SRID},createGeometryCollection:function(){if(0===arguments.length)return new ft(null,this);if(1===arguments.length){var t=arguments[0];return new ft(t,this)}},createGeometry:function(t){var e=new _t(this);return e.edit(t,{edit:function(){if(2===arguments.length){var t=arguments[0];arguments[1];return this.coordinateSequenceFactory.create(t)}}})},getPrecisionModel:function(){return this.precisionModel},createLinearRing:function(){if(0===arguments.length)return this.createLinearRing(this.getCoordinateSequenceFactory().create([]));if(1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];return this.createLinearRing(null!==t?this.getCoordinateSequenceFactory().create(t):null)}if(R(arguments[0],D)){var e=arguments[0];return new bt(e,this)}}},createMultiPolygon:function(){if(0===arguments.length)return new Ot(null,this);if(1===arguments.length){var t=arguments[0];return new Ot(t,this)}},createMultiPoint:function(){if(0===arguments.length)return new Pt(null,this);if(1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];return new Pt(t,this)}if(arguments[0]instanceof Array){var e=arguments[0];return this.createMultiPoint(null!==e?this.getCoordinateSequenceFactory().create(e):null)}if(R(arguments[0],D)){var n=arguments[0];if(null===n)return this.createMultiPoint(new Array(0).fill(null));for(var i=new Array(n.size()).fill(null),r=0;r<n.size();r++){var s=this.getCoordinateSequenceFactory().create(1,n.getDimension());Ct.copy(n,r,s,0,1),i[r]=this.createPoint(s)}return this.createMultiPoint(i)}}},interfaces_:function(){return[u]},getClass:function(){return ie}}),ie.toMultiPolygonArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toGeometryArray=function(t){if(null===t)return null;var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.getDefaultCoordinateSequenceFactory=function(){return qt.instance()},ie.toMultiLineStringArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toLineStringArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toMultiPointArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toLinearRingArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toPointArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toPolygonArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.createPointFromInternalCoord=function(t,e){return e.getPrecisionModel().makePrecise(t),e.getFactory().createPoint(t)},ie.serialVersionUID=-0x5ea75f2051eeb400;var oo={typeStr:/^\s*(\w+)\s*\(\s*(.*)\s*\)\s*$/,emptyTypeStr:/^\s*(\w+)\s*EMPTY\s*$/,spaces:/\s+/,parenComma:/\)\s*,\s*\(/,doubleParenComma:/\)\s*\)\s*,\s*\(\s*\(/,trimParens:/^\s*\(?(.*?)\)?\s*$/};e(re.prototype,{read:function(t){var e,n,i;t=t.replace(/[\n\r]/g," ");var r=oo.typeStr.exec(t);if(-1!==t.search("EMPTY")&&(r=oo.emptyTypeStr.exec(t),r[2]=void 0),r&&(n=r[1].toLowerCase(),i=r[2],uo[n]&&(e=uo[n].apply(this,[i]))),void 0===e)throw new Error("Could not parse WKT "+t);return e},write:function(t){return this.extractGeometry(t)},extractGeometry:function(t){var e=t.getGeometryType().toLowerCase();if(!ao[e])return null;var n,i=e.toUpperCase();return n=t.isEmpty()?i+" EMPTY":i+"("+ao[e].apply(this,[t])+")"}});var ao={coordinate:function(t){return t.x+" "+t.y},point:function(t){
return ao.coordinate.call(this,t.coordinates.coordinates[0])},multipoint:function(t){for(var e=[],n=0,i=t.geometries.length;i>n;++n)e.push("("+ao.point.apply(this,[t.geometries[n]])+")");return e.join(",")},linestring:function(t){for(var e=[],n=0,i=t.points.coordinates.length;i>n;++n)e.push(ao.coordinate.apply(this,[t.points.coordinates[n]]));return e.join(",")},linearring:function(t){for(var e=[],n=0,i=t.points.coordinates.length;i>n;++n)e.push(ao.coordinate.apply(this,[t.points.coordinates[n]]));return e.join(",")},multilinestring:function(t){for(var e=[],n=0,i=t.geometries.length;i>n;++n)e.push("("+ao.linestring.apply(this,[t.geometries[n]])+")");return e.join(",")},polygon:function(t){var e=[];e.push("("+ao.linestring.apply(this,[t.shell])+")");for(var n=0,i=t.holes.length;i>n;++n)e.push("("+ao.linestring.apply(this,[t.holes[n]])+")");return e.join(",")},multipolygon:function(t){for(var e=[],n=0,i=t.geometries.length;i>n;++n)e.push("("+ao.polygon.apply(this,[t.geometries[n]])+")");return e.join(",")},geometrycollection:function(t){for(var e=[],n=0,i=t.geometries.length;i>n;++n)e.push(this.extractGeometry(t.geometries[n]));return e.join(",")}},uo={point:function(t){if(void 0===t)return this.geometryFactory.createPoint();var e=t.trim().split(oo.spaces);return this.geometryFactory.createPoint(new g(Number.parseFloat(e[0]),Number.parseFloat(e[1])))},multipoint:function(t){if(void 0===t)return this.geometryFactory.createMultiPoint();for(var e,n=t.trim().split(","),i=[],r=0,s=n.length;s>r;++r)e=n[r].replace(oo.trimParens,"$1"),i.push(uo.point.apply(this,[e]));return this.geometryFactory.createMultiPoint(i)},linestring:function(t){if(void 0===t)return this.geometryFactory.createLineString();for(var e,n=t.trim().split(","),i=[],r=0,s=n.length;s>r;++r)e=n[r].trim().split(oo.spaces),i.push(new g(Number.parseFloat(e[0]),Number.parseFloat(e[1])));return this.geometryFactory.createLineString(i)},linearring:function(t){if(void 0===t)return this.geometryFactory.createLinearRing();for(var e,n=t.trim().split(","),i=[],r=0,s=n.length;s>r;++r)e=n[r].trim().split(oo.spaces),i.push(new g(Number.parseFloat(e[0]),Number.parseFloat(e[1])));return this.geometryFactory.createLinearRing(i)},multilinestring:function(t){if(void 0===t)return this.geometryFactory.createMultiLineString();for(var e,n=t.trim().split(oo.parenComma),i=[],r=0,s=n.length;s>r;++r)e=n[r].replace(oo.trimParens,"$1"),i.push(uo.linestring.apply(this,[e]));return this.geometryFactory.createMultiLineString(i)},polygon:function(t){if(void 0===t)return this.geometryFactory.createPolygon();for(var e,n,i,r,s=t.trim().split(oo.parenComma),o=[],a=0,u=s.length;u>a;++a)e=s[a].replace(oo.trimParens,"$1"),n=uo.linestring.apply(this,[e]),i=this.geometryFactory.createLinearRing(n.points),0===a?r=i:o.push(i);return this.geometryFactory.createPolygon(r,o)},multipolygon:function(t){if(void 0===t)return this.geometryFactory.createMultiPolygon();for(var e,n=t.trim().split(oo.doubleParenComma),i=[],r=0,s=n.length;s>r;++r)e=n[r].replace(oo.trimParens,"$1"),i.push(uo.polygon.apply(this,[e]));return this.geometryFactory.createMultiPolygon(i)},geometrycollection:function(t){if(void 0===t)return this.geometryFactory.createGeometryCollection();t=t.replace(/,\s*([A-Za-z])/g,"|$1");for(var e=t.trim().split("|"),n=[],i=0,r=e.length;r>i;++i)n.push(this.read(e[i]));return this.geometryFactory.createGeometryCollection(n)}};e(se.prototype,{write:function(t){return this.parser.write(t)}}),e(se,{toLineString:function(t,e){if(2!==arguments.length)throw new Error("Not implemented");return"LINESTRING ( "+t.x+" "+t.y+", "+e.x+" "+e.y+" )"}}),e(oe.prototype,{getIndexAlongSegment:function(t,e){return this.computeIntLineIndex(),this.intLineIndex[t][e]},getTopologySummary:function(){var t=new P;return this.isEndPoint()&&t.append(" endpoint"),this._isProper&&t.append(" proper"),this.isCollinear()&&t.append(" collinear"),t.toString()},computeIntersection:function(t,e,n,i){this.inputLines[0][0]=t,this.inputLines[0][1]=e,this.inputLines[1][0]=n,this.inputLines[1][1]=i,this.result=this.computeIntersect(t,e,n,i)},getIntersectionNum:function(){return this.result},computeIntLineIndex:function(){if(0===arguments.length)null===this.intLineIndex&&(this.intLineIndex=Array(2).fill().map(function(){return Array(2)}),this.computeIntLineIndex(0),this.computeIntLineIndex(1));else if(1===arguments.length){var t=arguments[0],e=this.getEdgeDistance(t,0),n=this.getEdgeDistance(t,1);e>n?(this.intLineIndex[t][0]=0,this.intLineIndex[t][1]=1):(this.intLineIndex[t][0]=1,this.intLineIndex[t][1]=0)}},isProper:function(){return this.hasIntersection()&&this._isProper},setPrecisionModel:function(t){this.precisionModel=t},isInteriorIntersection:function(){if(0===arguments.length)return this.isInteriorIntersection(0)?!0:!!this.isInteriorIntersection(1);if(1===arguments.length){for(var t=arguments[0],e=0;e<this.result;e++)if(!this.intPt[e].equals2D(this.inputLines[t][0])&&!this.intPt[e].equals2D(this.inputLines[t][1]))return!0;return!1}},getIntersection:function(t){return this.intPt[t]},isEndPoint:function(){return this.hasIntersection()&&!this._isProper},hasIntersection:function(){return this.result!==oe.NO_INTERSECTION},getEdgeDistance:function(t,e){var n=oe.computeEdgeDistance(this.intPt[e],this.inputLines[t][0],this.inputLines[t][1]);return n},isCollinear:function(){return this.result===oe.COLLINEAR_INTERSECTION},toString:function(){return se.toLineString(this.inputLines[0][0],this.inputLines[0][1])+" - "+se.toLineString(this.inputLines[1][0],this.inputLines[1][1])+this.getTopologySummary()},getEndpoint:function(t,e){return this.inputLines[t][e]},isIntersection:function(t){for(var e=0;e<this.result;e++)if(this.intPt[e].equals2D(t))return!0;return!1},getIntersectionAlongSegment:function(t,e){return this.computeIntLineIndex(),this.intPt[this.intLineIndex[t][e]]},interfaces_:function(){return[]},getClass:function(){return oe}}),oe.computeEdgeDistance=function(t,e,n){var i=Math.abs(n.x-e.x),r=Math.abs(n.y-e.y),s=-1;if(t.equals(e))s=0;else if(t.equals(n))s=i>r?i:r;else{var o=Math.abs(t.x-e.x),a=Math.abs(t.y-e.y);s=i>r?o:a,0!==s||t.equals(e)||(s=Math.max(o,a))}return f.isTrue(!(0===s&&!t.equals(e)),"Bad distance calculation"),s},oe.nonRobustComputeEdgeDistance=function(t,e,n){var i=t.x-e.x,r=t.y-e.y,s=Math.sqrt(i*i+r*r);return f.isTrue(!(0===s&&!t.equals(e)),"Invalid distance calculation"),s},oe.DONT_INTERSECT=0,oe.DO_INTERSECT=1,oe.COLLINEAR=2,oe.NO_INTERSECTION=0,oe.POINT_INTERSECTION=1,oe.COLLINEAR_INTERSECTION=2,h(ae,oe),e(ae.prototype,{isInSegmentEnvelopes:function(t){var e=new C(this.inputLines[0][0],this.inputLines[0][1]),n=new C(this.inputLines[1][0],this.inputLines[1][1]);return e.contains(t)&&n.contains(t)},computeIntersection:function(){if(3!==arguments.length)return oe.prototype.computeIntersection.apply(this,arguments);var t=arguments[0],e=arguments[1],n=arguments[2];return this._isProper=!1,C.intersects(e,n,t)&&0===he.orientationIndex(e,n,t)&&0===he.orientationIndex(n,e,t)?(this._isProper=!0,(t.equals(e)||t.equals(n))&&(this._isProper=!1),this.result=oe.POINT_INTERSECTION,null):void(this.result=oe.NO_INTERSECTION)},normalizeToMinimum:function(t,e,n,i,r){r.x=this.smallestInAbsValue(t.x,e.x,n.x,i.x),r.y=this.smallestInAbsValue(t.y,e.y,n.y,i.y),t.x-=r.x,t.y-=r.y,e.x-=r.x,e.y-=r.y,n.x-=r.x,n.y-=r.y,i.x-=r.x,i.y-=r.y},safeHCoordinateIntersection:function(t,e,n,i){var r=null;try{r=F.intersection(t,e,n,i)}catch(s){if(!(s instanceof w))throw s;r=ae.nearestEndpoint(t,e,n,i)}finally{}return r},intersection:function(t,e,n,i){var r=this.intersectionWithNormalization(t,e,n,i);return this.isInSegmentEnvelopes(r)||(r=new g(ae.nearestEndpoint(t,e,n,i))),null!==this.precisionModel&&this.precisionModel.makePrecise(r),r},smallestInAbsValue:function(t,e,n,i){var r=t,s=Math.abs(r);return Math.abs(e)<s&&(r=e,s=Math.abs(e)),Math.abs(n)<s&&(r=n,s=Math.abs(n)),Math.abs(i)<s&&(r=i),r},checkDD:function(t,e,n,i,r){var s=M.intersection(t,e,n,i),o=this.isInSegmentEnvelopes(s);A.out.println("DD in env = "+o+"  --------------------- "+s),r.distance(s)>1e-4&&A.out.println("Distance = "+r.distance(s))},intersectionWithNormalization:function(t,e,n,i){var r=new g(t),s=new g(e),o=new g(n),a=new g(i),u=new g;this.normalizeToEnvCentre(r,s,o,a,u);var l=this.safeHCoordinateIntersection(r,s,o,a);return l.x+=u.x,l.y+=u.y,l},computeCollinearIntersection:function(t,e,n,i){var r=C.intersects(t,e,n),s=C.intersects(t,e,i),o=C.intersects(n,i,t),a=C.intersects(n,i,e);return r&&s?(this.intPt[0]=n,this.intPt[1]=i,oe.COLLINEAR_INTERSECTION):o&&a?(this.intPt[0]=t,this.intPt[1]=e,oe.COLLINEAR_INTERSECTION):r&&o?(this.intPt[0]=n,this.intPt[1]=t,!n.equals(t)||s||a?oe.COLLINEAR_INTERSECTION:oe.POINT_INTERSECTION):r&&a?(this.intPt[0]=n,this.intPt[1]=e,!n.equals(e)||s||o?oe.COLLINEAR_INTERSECTION:oe.POINT_INTERSECTION):s&&o?(this.intPt[0]=i,this.intPt[1]=t,!i.equals(t)||r||a?oe.COLLINEAR_INTERSECTION:oe.POINT_INTERSECTION):s&&a?(this.intPt[0]=i,this.intPt[1]=e,!i.equals(e)||r||o?oe.COLLINEAR_INTERSECTION:oe.POINT_INTERSECTION):oe.NO_INTERSECTION},normalizeToEnvCentre:function(t,e,n,i,r){var s=t.x<e.x?t.x:e.x,o=t.y<e.y?t.y:e.y,a=t.x>e.x?t.x:e.x,u=t.y>e.y?t.y:e.y,l=n.x<i.x?n.x:i.x,h=n.y<i.y?n.y:i.y,c=n.x>i.x?n.x:i.x,f=n.y>i.y?n.y:i.y,g=s>l?s:l,d=c>a?a:c,p=o>h?o:h,v=f>u?u:f,m=(g+d)/2,y=(p+v)/2;r.x=m,r.y=y,t.x-=r.x,t.y-=r.y,e.x-=r.x,e.y-=r.y,n.x-=r.x,n.y-=r.y,i.x-=r.x,i.y-=r.y},computeIntersect:function(t,e,n,i){if(this._isProper=!1,!C.intersects(t,e,n,i))return oe.NO_INTERSECTION;var r=he.orientationIndex(t,e,n),s=he.orientationIndex(t,e,i);if(r>0&&s>0||0>r&&0>s)return oe.NO_INTERSECTION;var o=he.orientationIndex(n,i,t),a=he.orientationIndex(n,i,e);if(o>0&&a>0||0>o&&0>a)return oe.NO_INTERSECTION;var u=0===r&&0===s&&0===o&&0===a;return u?this.computeCollinearIntersection(t,e,n,i):(0===r||0===s||0===o||0===a?(this._isProper=!1,t.equals2D(n)||t.equals2D(i)?this.intPt[0]=t:e.equals2D(n)||e.equals2D(i)?this.intPt[0]=e:0===r?this.intPt[0]=new g(n):0===s?this.intPt[0]=new g(i):0===o?this.intPt[0]=new g(t):0===a&&(this.intPt[0]=new g(e))):(this._isProper=!0,this.intPt[0]=this.intersection(t,e,n,i)),oe.POINT_INTERSECTION)},interfaces_:function(){return[]},getClass:function(){return ae}}),ae.nearestEndpoint=function(t,e,n,i){var r=t,s=he.distancePointLine(t,n,i),o=he.distancePointLine(e,n,i);return s>o&&(s=o,r=e),o=he.distancePointLine(n,t,e),s>o&&(s=o,r=n),o=he.distancePointLine(i,t,e),s>o&&(s=o,r=i),r},e(ue.prototype,{interfaces_:function(){return[]},getClass:function(){return ue}}),ue.orientationIndex=function(t,e,n){var i=e.x-t.x,r=e.y-t.y,s=n.x-e.x,o=n.y-e.y;return ue.signOfDet2x2(i,r,s,o)},ue.signOfDet2x2=function(t,e,n,i){var r=null,s=null,o=null,a=0;if(r=1,0===t||0===i)return 0===e||0===n?0:e>0?n>0?-r:r:n>0?r:-r;if(0===e||0===n)return i>0?t>0?r:-r:t>0?-r:r;if(e>0?i>0?i>=e||(r=-r,s=t,t=n,n=s,s=e,e=i,i=s):-i>=e?(r=-r,n=-n,i=-i):(s=t,t=-n,n=s,s=e,e=-i,i=s):i>0?i>=-e?(r=-r,t=-t,e=-e):(s=-t,t=n,n=s,s=-e,e=i,i=s):e>=i?(t=-t,e=-e,n=-n,i=-i):(r=-r,s=-t,t=-n,n=s,s=-e,e=-i,i=s),t>0){if(!(n>0))return r;if(!(n>=t))return r}else{if(n>0)return-r;if(!(t>=n))return-r;r=-r,t=-t,n=-n}for(;;){if(a+=1,o=Math.floor(n/t),n-=o*t,i-=o*e,0>i)return-r;if(i>e)return r;if(t>n+n){if(i+i>e)return r}else{if(e>i+i)return-r;n=t-n,i=e-i,r=-r}if(0===i)return 0===n?0:-r;if(0===n)return r;if(o=Math.floor(t/n),t-=o*n,e-=o*i,0>e)return r;if(e>i)return-r;if(n>t+t){if(e+e>i)return-r}else{if(i>e+e)return r;t=n-t,e=i-e,r=-r}if(0===e)return 0===t?0:r;if(0===t)return-r}},e(le.prototype,{countSegment:function(t,e){if(t.x<this.p.x&&e.x<this.p.x)return null;if(this.p.x===e.x&&this.p.y===e.y)return this.isPointOnSegment=!0,null;if(t.y===this.p.y&&e.y===this.p.y){var n=t.x,i=e.x;return n>i&&(n=e.x,i=t.x),this.p.x>=n&&this.p.x<=i&&(this.isPointOnSegment=!0),null}if(t.y>this.p.y&&e.y<=this.p.y||e.y>this.p.y&&t.y<=this.p.y){var r=t.x-this.p.x,s=t.y-this.p.y,o=e.x-this.p.x,a=e.y-this.p.y,u=ue.signOfDet2x2(r,s,o,a);if(0===u)return this.isPointOnSegment=!0,null;s>a&&(u=-u),u>0&&this.crossingCount++}},isPointInPolygon:function(){return this.getLocation()!==L.EXTERIOR},getLocation:function(){return this.isPointOnSegment?L.BOUNDARY:this.crossingCount%2===1?L.INTERIOR:L.EXTERIOR},isOnSegment:function(){return this.isPointOnSegment},interfaces_:function(){return[]},getClass:function(){return le}}),le.locatePointInRing=function(){if(arguments[0]instanceof g&&R(arguments[1],D)){for(var t=arguments[0],e=arguments[1],n=new le(t),i=new g,r=new g,s=1;s<e.size();s++)if(e.getCoordinate(s,i),e.getCoordinate(s-1,r),n.countSegment(i,r),n.isOnSegment())return n.getLocation();return n.getLocation()}if(arguments[0]instanceof g&&arguments[1]instanceof Array){for(var o=arguments[0],a=arguments[1],n=new le(o),s=1;s<a.length;s++){var i=a[s],r=a[s-1];if(n.countSegment(i,r),n.isOnSegment())return n.getLocation()}return n.getLocation()}},e(he.prototype,{interfaces_:function(){return[]},getClass:function(){return he}}),he.orientationIndex=function(t,e,n){return M.orientationIndex(t,e,n)},he.signedArea=function(){if(arguments[0]instanceof Array){var t=arguments[0];if(t.length<3)return 0;for(var e=0,n=t[0].x,i=1;i<t.length-1;i++){var r=t[i].x-n,s=t[i+1].y,o=t[i-1].y;e+=r*(o-s)}return e/2}if(R(arguments[0],D)){var a=arguments[0],u=a.size();if(3>u)return 0;var l=new g,h=new g,c=new g;a.getCoordinate(0,h),a.getCoordinate(1,c);var n=h.x;c.x-=n;for(var e=0,i=1;u-1>i;i++)l.y=h.y,h.x=c.x,h.y=c.y,a.getCoordinate(i+1,c),c.x-=n,e+=h.x*(l.y-c.y);return e/2}},he.distanceLineLine=function(t,e,n,i){if(t.equals(e))return he.distancePointLine(t,n,i);if(n.equals(i))return he.distancePointLine(i,t,e);var r=!1;if(C.intersects(t,e,n,i)){var s=(e.x-t.x)*(i.y-n.y)-(e.y-t.y)*(i.x-n.x);if(0===s)r=!0;else{var o=(t.y-n.y)*(i.x-n.x)-(t.x-n.x)*(i.y-n.y),a=(t.y-n.y)*(e.x-t.x)-(t.x-n.x)*(e.y-t.y),u=a/s,l=o/s;(0>l||l>1||0>u||u>1)&&(r=!0)}}else r=!0;return r?T.min(he.distancePointLine(t,n,i),he.distancePointLine(e,n,i),he.distancePointLine(n,t,e),he.distancePointLine(i,t,e)):0},he.isPointInRing=function(t,e){return he.locatePointInRing(t,e)!==L.EXTERIOR},he.computeLength=function(t){var e=t.size();if(1>=e)return 0;var n=0,i=new g;t.getCoordinate(0,i);for(var r=i.x,s=i.y,o=1;e>o;o++){t.getCoordinate(o,i);var a=i.x,u=i.y,l=a-r,h=u-s;n+=Math.sqrt(l*l+h*h),r=a,s=u}return n},he.isCCW=function(t){var e=t.length-1;if(3>e)throw new i("Ring has fewer than 4 points, so orientation cannot be determined");for(var n=t[0],r=0,s=1;e>=s;s++){var o=t[s];o.y>n.y&&(n=o,r=s)}var a=r;do a-=1,0>a&&(a=e);while(t[a].equals2D(n)&&a!==r);var u=r;do u=(u+1)%e;while(t[u].equals2D(n)&&u!==r);var l=t[a],h=t[u];if(l.equals2D(n)||h.equals2D(n)||l.equals2D(h))return!1;var c=he.computeOrientation(l,n,h),f=!1;return f=0===c?l.x>h.x:c>0},he.locatePointInRing=function(t,e){return le.locatePointInRing(t,e)},he.distancePointLinePerpendicular=function(t,e,n){var i=(n.x-e.x)*(n.x-e.x)+(n.y-e.y)*(n.y-e.y),r=((e.y-t.y)*(n.x-e.x)-(e.x-t.x)*(n.y-e.y))/i;return Math.abs(r)*Math.sqrt(i)},he.computeOrientation=function(t,e,n){return he.orientationIndex(t,e,n)},he.distancePointLine=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(0===e.length)throw new i("Line array must contain at least one vertex");for(var n=t.distance(e[0]),r=0;r<e.length-1;r++){var s=he.distancePointLine(t,e[r],e[r+1]);n>s&&(n=s)}return n}if(3===arguments.length){var o=arguments[0],a=arguments[1],u=arguments[2];if(a.x===u.x&&a.y===u.y)return o.distance(a);var l=(u.x-a.x)*(u.x-a.x)+(u.y-a.y)*(u.y-a.y),h=((o.x-a.x)*(u.x-a.x)+(o.y-a.y)*(u.y-a.y))/l;if(0>=h)return o.distance(a);if(h>=1)return o.distance(u);var c=((a.y-o.y)*(u.x-a.x)-(a.x-o.x)*(u.y-a.y))/l;return Math.abs(c)*Math.sqrt(l)}},he.isOnLine=function(t,e){for(var n=new ae,i=1;i<e.length;i++){var r=e[i-1],s=e[i];if(n.computeIntersection(t,r,s),n.hasIntersection())return!0}return!1},he.CLOCKWISE=-1,he.RIGHT=he.CLOCKWISE,he.COUNTERCLOCKWISE=1,he.LEFT=he.COUNTERCLOCKWISE,he.COLLINEAR=0,he.STRAIGHT=he.COLLINEAR,e(ce.prototype,{minX:function(){return Math.min(this.p0.x,this.p1.x)},orientationIndex:function(){if(arguments[0]instanceof ce){var t=arguments[0],e=he.orientationIndex(this.p0,this.p1,t.p0),n=he.orientationIndex(this.p0,this.p1,t.p1);return e>=0&&n>=0?Math.max(e,n):0>=e&&0>=n?Math.max(e,n):0}if(arguments[0]instanceof g){var i=arguments[0];return he.orientationIndex(this.p0,this.p1,i)}},toGeometry:function(t){return t.createLineString([this.p0,this.p1])},isVertical:function(){return this.p0.x===this.p1.x},equals:function(t){if(!(t instanceof ce))return!1;var e=t;return this.p0.equals(e.p0)&&this.p1.equals(e.p1)},intersection:function(t){var e=new ae;return e.computeIntersection(this.p0,this.p1,t.p0,t.p1),e.hasIntersection()?e.getIntersection(0):null},project:function(){if(arguments[0]instanceof g){var t=arguments[0];if(t.equals(this.p0)||t.equals(this.p1))return new g(t);var e=this.projectionFactor(t),n=new g;return n.x=this.p0.x+e*(this.p1.x-this.p0.x),n.y=this.p0.y+e*(this.p1.y-this.p0.y),n}if(arguments[0]instanceof ce){var i=arguments[0],r=this.projectionFactor(i.p0),s=this.projectionFactor(i.p1);if(r>=1&&s>=1)return null;if(0>=r&&0>=s)return null;var o=this.project(i.p0);0>r&&(o=this.p0),r>1&&(o=this.p1);var a=this.project(i.p1);return 0>s&&(a=this.p0),s>1&&(a=this.p1),new ce(o,a)}},normalize:function(){this.p1.compareTo(this.p0)<0&&this.reverse()},angle:function(){return Math.atan2(this.p1.y-this.p0.y,this.p1.x-this.p0.x)},getCoordinate:function(t){return 0===t?this.p0:this.p1},distancePerpendicular:function(t){return he.distancePointLinePerpendicular(t,this.p0,this.p1)},minY:function(){return Math.min(this.p0.y,this.p1.y)},midPoint:function(){return ce.midPoint(this.p0,this.p1)},projectionFactor:function(t){if(t.equals(this.p0))return 0;if(t.equals(this.p1))return 1;var e=this.p1.x-this.p0.x,n=this.p1.y-this.p0.y,i=e*e+n*n;if(0>=i)return r.NaN;var s=((t.x-this.p0.x)*e+(t.y-this.p0.y)*n)/i;return s},closestPoints:function(t){var e=this.intersection(t);if(null!==e)return[e,e];var n=new Array(2).fill(null),i=r.MAX_VALUE,s=null,o=this.closestPoint(t.p0);i=o.distance(t.p0),n[0]=o,n[1]=t.p0;var a=this.closestPoint(t.p1);s=a.distance(t.p1),i>s&&(i=s,n[0]=a,n[1]=t.p1);var u=t.closestPoint(this.p0);s=u.distance(this.p0),i>s&&(i=s,n[0]=this.p0,n[1]=u);var l=t.closestPoint(this.p1);return s=l.distance(this.p1),i>s&&(i=s,n[0]=this.p1,n[1]=l),n},closestPoint:function(t){var e=this.projectionFactor(t);if(e>0&&1>e)return this.project(t);var n=this.p0.distance(t),i=this.p1.distance(t);return i>n?this.p0:this.p1},maxX:function(){return Math.max(this.p0.x,this.p1.x)},getLength:function(){return this.p0.distance(this.p1)},compareTo:function(t){var e=t,n=this.p0.compareTo(e.p0);return 0!==n?n:this.p1.compareTo(e.p1)},reverse:function(){var t=this.p0;this.p0=this.p1,this.p1=t},equalsTopo:function(t){return this.p0.equals(t.p0)&&this.p1.equals(t.p1)||this.p0.equals(t.p1)&&this.p1.equals(t.p0)},lineIntersection:function(t){try{var e=F.intersection(this.p0,this.p1,t.p0,t.p1);return e}catch(t){if(!(t instanceof w))throw t}finally{}return null},maxY:function(){return Math.max(this.p0.y,this.p1.y)},pointAlongOffset:function(t,e){var n=this.p0.x+t*(this.p1.x-this.p0.x),i=this.p0.y+t*(this.p1.y-this.p0.y),r=this.p1.x-this.p0.x,s=this.p1.y-this.p0.y,o=Math.sqrt(r*r+s*s),a=0,u=0;if(0!==e){if(0>=o)throw new IllegalStateException("Cannot compute offset from zero-length line segment");a=e*r/o,u=e*s/o}var l=n-u,h=i+a,c=new g(l,h);return c},setCoordinates:function(){if(1===arguments.length){var t=arguments[0];this.setCoordinates(t.p0,t.p1)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.p0.x=e.x,this.p0.y=e.y,this.p1.x=n.x,this.p1.y=n.y}},segmentFraction:function(t){var e=this.projectionFactor(t);return 0>e?e=0:(e>1||r.isNaN(e))&&(e=1),e},toString:function(){return"LINESTRING( "+this.p0.x+" "+this.p0.y+", "+this.p1.x+" "+this.p1.y+")"},isHorizontal:function(){return this.p0.y===this.p1.y},distance:function(){if(arguments[0]instanceof ce){var t=arguments[0];return he.distanceLineLine(this.p0,this.p1,t.p0,t.p1)}if(arguments[0]instanceof g){var e=arguments[0];return he.distancePointLine(e,this.p0,this.p1)}},pointAlong:function(t){var e=new g;return e.x=this.p0.x+t*(this.p1.x-this.p0.x),e.y=this.p0.y+t*(this.p1.y-this.p0.y),e},hashCode:function(){var t=java.lang.Double.doubleToLongBits(this.p0.x);t^=31*java.lang.Double.doubleToLongBits(this.p0.y);var e=Math.trunc(t)^Math.trunc(t>>32),n=java.lang.Double.doubleToLongBits(this.p1.x);n^=31*java.lang.Double.doubleToLongBits(this.p1.y);var i=Math.trunc(n)^Math.trunc(n>>32);return e^i},interfaces_:function(){return[s,u]},getClass:function(){return ce}}),ce.midPoint=function(t,e){return new g((t.x+e.x)/2,(t.y+e.y)/2)},ce.serialVersionUID=0x2d2172135f411c00,e(fe.prototype,{isIntersects:function(){return!this.isDisjoint()},isCovers:function(){var t=fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])||fe.isTrue(this.matrix[L.INTERIOR][L.BOUNDARY])||fe.isTrue(this.matrix[L.BOUNDARY][L.INTERIOR])||fe.isTrue(this.matrix[L.BOUNDARY][L.BOUNDARY]);return t&&this.matrix[L.EXTERIOR][L.INTERIOR]===lt.FALSE&&this.matrix[L.EXTERIOR][L.BOUNDARY]===lt.FALSE},isCoveredBy:function(){var t=fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])||fe.isTrue(this.matrix[L.INTERIOR][L.BOUNDARY])||fe.isTrue(this.matrix[L.BOUNDARY][L.INTERIOR])||fe.isTrue(this.matrix[L.BOUNDARY][L.BOUNDARY]);return t&&this.matrix[L.INTERIOR][L.EXTERIOR]===lt.FALSE&&this.matrix[L.BOUNDARY][L.EXTERIOR]===lt.FALSE},set:function(){if(1===arguments.length)for(var t=arguments[0],e=0;e<t.length;e++){var n=Math.trunc(e/3),i=e%3;this.matrix[n][i]=lt.toDimensionValue(t.charAt(e))}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];this.matrix[r][s]=o}},isContains:function(){return fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&this.matrix[L.EXTERIOR][L.INTERIOR]===lt.FALSE&&this.matrix[L.EXTERIOR][L.BOUNDARY]===lt.FALSE},setAtLeast:function(){if(1===arguments.length)for(var t=arguments[0],e=0;e<t.length;e++){var n=Math.trunc(e/3),i=e%3;this.setAtLeast(n,i,lt.toDimensionValue(t.charAt(e)))}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];this.matrix[r][s]<o&&(this.matrix[r][s]=o)}},setAtLeastIfValid:function(t,e,n){t>=0&&e>=0&&this.setAtLeast(t,e,n)},isWithin:function(){return fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&this.matrix[L.INTERIOR][L.EXTERIOR]===lt.FALSE&&this.matrix[L.BOUNDARY][L.EXTERIOR]===lt.FALSE},isTouches:function(t,e){return t>e?this.isTouches(e,t):t===lt.A&&e===lt.A||t===lt.L&&e===lt.L||t===lt.L&&e===lt.A||t===lt.P&&e===lt.A||t===lt.P&&e===lt.L?this.matrix[L.INTERIOR][L.INTERIOR]===lt.FALSE&&(fe.isTrue(this.matrix[L.INTERIOR][L.BOUNDARY])||fe.isTrue(this.matrix[L.BOUNDARY][L.INTERIOR])||fe.isTrue(this.matrix[L.BOUNDARY][L.BOUNDARY])):!1},isOverlaps:function(t,e){return t===lt.P&&e===lt.P||t===lt.A&&e===lt.A?fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&fe.isTrue(this.matrix[L.INTERIOR][L.EXTERIOR])&&fe.isTrue(this.matrix[L.EXTERIOR][L.INTERIOR]):t===lt.L&&e===lt.L?1===this.matrix[L.INTERIOR][L.INTERIOR]&&fe.isTrue(this.matrix[L.INTERIOR][L.EXTERIOR])&&fe.isTrue(this.matrix[L.EXTERIOR][L.INTERIOR]):!1},isEquals:function(t,e){return t!==e?!1:fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&this.matrix[L.INTERIOR][L.EXTERIOR]===lt.FALSE&&this.matrix[L.BOUNDARY][L.EXTERIOR]===lt.FALSE&&this.matrix[L.EXTERIOR][L.INTERIOR]===lt.FALSE&&this.matrix[L.EXTERIOR][L.BOUNDARY]===lt.FALSE},toString:function(){for(var t=new P("123456789"),e=0;3>e;e++)for(var n=0;3>n;n++)t.setCharAt(3*e+n,lt.toDimensionSymbol(this.matrix[e][n]));return t.toString()},setAll:function(t){for(var e=0;3>e;e++)for(var n=0;3>n;n++)this.matrix[e][n]=t},get:function(t,e){return this.matrix[t][e]},transpose:function(){var t=this.matrix[1][0];return this.matrix[1][0]=this.matrix[0][1],this.matrix[0][1]=t,t=this.matrix[2][0],this.matrix[2][0]=this.matrix[0][2],this.matrix[0][2]=t,t=this.matrix[2][1],this.matrix[2][1]=this.matrix[1][2],this.matrix[1][2]=t,this},matches:function(t){if(9!==t.length)throw new i("Should be length 9: "+t);for(var e=0;3>e;e++)for(var n=0;3>n;n++)if(!fe.matches(this.matrix[e][n],t.charAt(3*e+n)))return!1;return!0},add:function(t){for(var e=0;3>e;e++)for(var n=0;3>n;n++)this.setAtLeast(e,n,t.get(e,n))},isDisjoint:function(){return this.matrix[L.INTERIOR][L.INTERIOR]===lt.FALSE&&this.matrix[L.INTERIOR][L.BOUNDARY]===lt.FALSE&&this.matrix[L.BOUNDARY][L.INTERIOR]===lt.FALSE&&this.matrix[L.BOUNDARY][L.BOUNDARY]===lt.FALSE},isCrosses:function(t,e){return t===lt.P&&e===lt.L||t===lt.P&&e===lt.A||t===lt.L&&e===lt.A?fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&fe.isTrue(this.matrix[L.INTERIOR][L.EXTERIOR]):t===lt.L&&e===lt.P||t===lt.A&&e===lt.P||t===lt.A&&e===lt.L?fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&fe.isTrue(this.matrix[L.EXTERIOR][L.INTERIOR]):t===lt.L&&e===lt.L?0===this.matrix[L.INTERIOR][L.INTERIOR]:!1},interfaces_:function(){return[o]},getClass:function(){return fe}}),fe.matches=function(){if(Number.isInteger(arguments[0])&&"string"==typeof arguments[1]){var t=arguments[0],e=arguments[1];return e===lt.SYM_DONTCARE?!0:e===lt.SYM_TRUE&&(t>=0||t===lt.TRUE)?!0:e===lt.SYM_FALSE&&t===lt.FALSE?!0:e===lt.SYM_P&&t===lt.P?!0:e===lt.SYM_L&&t===lt.L?!0:e===lt.SYM_A&&t===lt.A}if("string"==typeof arguments[0]&&"string"==typeof arguments[1]){var n=arguments[0],i=arguments[1],r=new fe(n);return r.matches(i)}},fe.isTrue=function(t){return t>=0||t===lt.TRUE};var lo=Object.freeze({Coordinate:g,CoordinateList:N,Envelope:C,LineSegment:ce,GeometryFactory:ie,Geometry:B,Point:Lt,LineString:St,LinearRing:bt,Polygon:Tt,GeometryCollection:ft,MultiPoint:Pt,MultiLineString:gt,MultiPolygon:Ot,Dimension:lt,IntersectionMatrix:fe});e(ge.prototype,{addPoint:function(t){this.ptCount+=1,this.ptCentSum.x+=t.x,this.ptCentSum.y+=t.y},setBasePoint:function(t){null===this.areaBasePt&&(this.areaBasePt=t)},addLineSegments:function(t){for(var e=0,n=0;n<t.length-1;n++){var i=t[n].distance(t[n+1]);if(0!==i){e+=i;var r=(t[n].x+t[n+1].x)/2;this.lineCentSum.x+=i*r;var s=(t[n].y+t[n+1].y)/2;this.lineCentSum.y+=i*s}}this.totalLength+=e,0===e&&t.length>0&&this.addPoint(t[0])},addHole:function(t){for(var e=he.isCCW(t),n=0;n<t.length-1;n++)this.addTriangle(this.areaBasePt,t[n],t[n+1],e);this.addLineSegments(t)},getCentroid:function(){var t=new g;if(Math.abs(this.areasum2)>0)t.x=this.cg3.x/3/this.areasum2,t.y=this.cg3.y/3/this.areasum2;else if(this.totalLength>0)t.x=this.lineCentSum.x/this.totalLength,t.y=this.lineCentSum.y/this.totalLength;else{if(!(this.ptCount>0))return null;t.x=this.ptCentSum.x/this.ptCount,t.y=this.ptCentSum.y/this.ptCount}return t},addShell:function(t){t.length>0&&this.setBasePoint(t[0]);for(var e=!he.isCCW(t),n=0;n<t.length-1;n++)this.addTriangle(this.areaBasePt,t[n],t[n+1],e);this.addLineSegments(t)},addTriangle:function(t,e,n,i){var r=i?1:-1;ge.centroid3(t,e,n,this.triangleCent3);var s=ge.area2(t,e,n);this.cg3.x+=r*s*this.triangleCent3.x,this.cg3.y+=r*s*this.triangleCent3.y,this.areasum2+=r*s},add:function(){if(arguments[0]instanceof Tt){var t=arguments[0];this.addShell(t.getExteriorRing().getCoordinates());for(var e=0;e<t.getNumInteriorRing();e++)this.addHole(t.getInteriorRingN(e).getCoordinates())}else if(arguments[0]instanceof B){var n=arguments[0];if(n.isEmpty())return null;if(n instanceof Lt)this.addPoint(n.getCoordinate());else if(n instanceof St)this.addLineSegments(n.getCoordinates());else if(n instanceof Tt){var i=n;this.add(i)}else if(n instanceof ft)for(var r=n,e=0;e<r.getNumGeometries();e++)this.add(r.getGeometryN(e))}},interfaces_:function(){return[]},getClass:function(){return ge}}),ge.area2=function(t,e,n){return(e.x-t.x)*(n.y-t.y)-(n.x-t.x)*(e.y-t.y)},ge.centroid3=function(t,e,n,i){return i.x=t.x+e.x+n.x,i.y=t.y+e.y+n.y,null},ge.getCentroid=function(t){var e=new ge(t);return e.getCentroid()},de.prototype=new Error,de.prototype.name="EmptyStackException",pe.prototype=new y,pe.prototype.add=function(t){return this.array_.push(t),!0},pe.prototype.get=function(t){if(0>t||t>=this.size())throw new IndexOutOfBoundsException;return this.array_[t]},pe.prototype.push=function(t){return this.array_.push(t),t},pe.prototype.pop=function(t){if(0===this.array_.length)throw new de;return this.array_.pop()},pe.prototype.peek=function(){if(0===this.array_.length)throw new de;return this.array_[this.array_.length-1]},pe.prototype.empty=function(){return 0===this.array_.length},pe.prototype.isEmpty=function(){return this.empty()},pe.prototype.search=function(t){return this.array_.indexOf(t)},pe.prototype.size=function(){return this.array_.length},pe.prototype.toArray=function(){for(var t=[],e=0,n=this.array_.length;n>e;e++)t.push(this.array_[e]);return t},e(ve.prototype,{filter:function(t){this.treeSet.contains(t)||(this.list.add(t),this.treeSet.add(t))},getCoordinates:function(){var t=new Array(this.list.size()).fill(null);return this.list.toArray(t)},interfaces_:function(){return[z]},getClass:function(){return ve}}),ve.filterCoordinates=function(t){for(var e=new ve,n=0;n<t.length;n++)e.filter(t[n]);return e.getCoordinates()},e(me.prototype,{preSort:function(t){for(var e=null,n=1;n<t.length;n++)(t[n].y<t[0].y||t[n].y===t[0].y&&t[n].x<t[0].x)&&(e=t[0],t[0]=t[n],t[n]=e);return ut.sort(t,1,t.length,new ye(t[0])),t},computeOctRing:function(t){var e=this.computeOctPts(t),n=new N;return n.add(e,!1),n.size()<3?null:(n.closeRing(),n.toCoordinateArray())},lineOrPolygon:function(t){if(t=this.cleanRing(t),3===t.length)return this.geomFactory.createLineString([t[0],t[1]]);var e=this.geomFactory.createLinearRing(t);return this.geomFactory.createPolygon(e,null)},cleanRing:function(t){f.equals(t[0],t[t.length-1]);for(var e=new I,n=null,i=0;i<=t.length-2;i++){var r=t[i],s=t[i+1];r.equals(s)||null!==n&&this.isBetween(n,r,s)||(e.add(r),n=r)}e.add(t[t.length-1]);var o=new Array(e.size()).fill(null);return e.toArray(o)},isBetween:function(t,e,n){if(0!==he.computeOrientation(t,e,n))return!1;if(t.x!==n.x){if(t.x<=e.x&&e.x<=n.x)return!0;if(n.x<=e.x&&e.x<=t.x)return!0}if(t.y!==n.y){if(t.y<=e.y&&e.y<=n.y)return!0;if(n.y<=e.y&&e.y<=t.y)return!0}return!1},reduce:function(t){var e=this.computeOctRing(t);if(null===e)return t;for(var n=new at,i=0;i<e.length;i++)n.add(e[i]);for(var i=0;i<t.length;i++)he.isPointInRing(t[i],e)||n.add(t[i]);var r=H.toCoordinateArray(n);return r.length<3?this.padArray3(r):r},getConvexHull:function(){if(0===this.inputPts.length)return this.geomFactory.createGeometryCollection(null);if(1===this.inputPts.length)return this.geomFactory.createPoint(this.inputPts[0]);if(2===this.inputPts.length)return this.geomFactory.createLineString(this.inputPts);var t=this.inputPts;this.inputPts.length>50&&(t=this.reduce(this.inputPts));var e=this.preSort(t),n=this.grahamScan(e),i=this.toCoordinateArray(n);return this.lineOrPolygon(i)},padArray3:function(t){for(var e=new Array(3).fill(null),n=0;n<e.length;n++)n<t.length?e[n]=t[n]:e[n]=t[0];return e},computeOctPts:function(t){for(var e=new Array(8).fill(null),n=0;n<e.length;n++)e[n]=t[0];for(var i=1;i<t.length;i++)t[i].x<e[0].x&&(e[0]=t[i]),t[i].x-t[i].y<e[1].x-e[1].y&&(e[1]=t[i]),t[i].y>e[2].y&&(e[2]=t[i]),t[i].x+t[i].y>e[3].x+e[3].y&&(e[3]=t[i]),t[i].x>e[4].x&&(e[4]=t[i]),t[i].x-t[i].y>e[5].x-e[5].y&&(e[5]=t[i]),t[i].y<e[6].y&&(e[6]=t[i]),t[i].x+t[i].y<e[7].x+e[7].y&&(e[7]=t[i]);return e},toCoordinateArray:function(t){for(var e=new Array(t.size()).fill(null),n=0;n<t.size();n++){var i=t.get(n);e[n]=i}return e},grahamScan:function(t){var e=null,n=new pe;e=n.push(t[0]),e=n.push(t[1]),e=n.push(t[2]);for(var i=3;i<t.length;i++){for(e=n.pop();!n.empty()&&he.computeOrientation(n.peek(),e,t[i])>0;)e=n.pop();e=n.push(e),e=n.push(t[i])}return e=n.push(t[0]),n},interfaces_:function(){return[]},getClass:function(){return me}}),me.extractCoordinates=function(t){var e=new ve;return t.apply(e),e.getCoordinates()},e(ye.prototype,{compare:function(t,e){var n=t,i=e;return ye.polarCompare(this.origin,n,i)},interfaces_:function(){return[a]},getClass:function(){return ye}}),ye.polarCompare=function(t,e,n){var i=e.x-t.x,r=e.y-t.y,s=n.x-t.x,o=n.y-t.y,a=he.computeOrientation(t,e,n);
if(a===he.COUNTERCLOCKWISE)return 1;if(a===he.CLOCKWISE)return-1;var u=i*i+r*r,l=s*s+o*o;return l>u?-1:u>l?1:0},me.RadialComparator=ye,e(xe.prototype,{transformPoint:function(t,e){return this.factory.createPoint(this.transformCoordinates(t.getCoordinateSequence(),t))},transformPolygon:function(t,e){var n=!0,i=this.transformLinearRing(t.getExteriorRing(),t);null!==i&&i instanceof bt&&!i.isEmpty()||(n=!1);for(var r=new I,s=0;s<t.getNumInteriorRing();s++){var o=this.transformLinearRing(t.getInteriorRingN(s),t);null===o||o.isEmpty()||(o instanceof bt||(n=!1),r.add(o))}if(n)return this.factory.createPolygon(i,r.toArray([]));var a=new I;return null!==i&&a.add(i),a.addAll(r),this.factory.buildGeometry(a)},createCoordinateSequence:function(t){return this.factory.getCoordinateSequenceFactory().create(t)},getInputGeometry:function(){return this.inputGeom},transformMultiLineString:function(t,e){for(var n=new I,i=0;i<t.getNumGeometries();i++){var r=this.transformLineString(t.getGeometryN(i),t);null!==r&&(r.isEmpty()||n.add(r))}return this.factory.buildGeometry(n)},transformCoordinates:function(t,e){return this.copy(t)},transformLineString:function(t,e){return this.factory.createLineString(this.transformCoordinates(t.getCoordinateSequence(),t))},transformMultiPoint:function(t,e){for(var n=new I,i=0;i<t.getNumGeometries();i++){var r=this.transformPoint(t.getGeometryN(i),t);null!==r&&(r.isEmpty()||n.add(r))}return this.factory.buildGeometry(n)},transformMultiPolygon:function(t,e){for(var n=new I,i=0;i<t.getNumGeometries();i++){var r=this.transformPolygon(t.getGeometryN(i),t);null!==r&&(r.isEmpty()||n.add(r))}return this.factory.buildGeometry(n)},copy:function(t){return t.copy()},transformGeometryCollection:function(t,e){for(var n=new I,i=0;i<t.getNumGeometries();i++){var r=this.transform(t.getGeometryN(i));null!==r&&(this.pruneEmptyGeometry&&r.isEmpty()||n.add(r))}return this.preserveGeometryCollectionType?this.factory.createGeometryCollection(ie.toGeometryArray(n)):this.factory.buildGeometry(n)},transform:function(t){if(this.inputGeom=t,this.factory=t.getFactory(),t instanceof Lt)return this.transformPoint(t,null);if(t instanceof Pt)return this.transformMultiPoint(t,null);if(t instanceof bt)return this.transformLinearRing(t,null);if(t instanceof St)return this.transformLineString(t,null);if(t instanceof gt)return this.transformMultiLineString(t,null);if(t instanceof Tt)return this.transformPolygon(t,null);if(t instanceof Ot)return this.transformMultiPolygon(t,null);if(t instanceof ft)return this.transformGeometryCollection(t,null);throw new i("Unknown Geometry subtype: "+t.getClass().getName())},transformLinearRing:function(t,e){var n=this.transformCoordinates(t.getCoordinateSequence(),t);if(null===n)return this.factory.createLinearRing(null);var i=n.size();return i>0&&4>i&&!this.preserveType?this.factory.createLineString(n):this.factory.createLinearRing(n)},interfaces_:function(){return[]},getClass:function(){return xe}}),e(Ee.prototype,{snapVertices:function(t,e){for(var n=this._isClosed?t.size()-1:t.size(),i=0;n>i;i++){var r=t.get(i),s=this.findSnapForVertex(r,e);null!==s&&(t.set(i,new g(s)),0===i&&this._isClosed&&t.set(t.size()-1,new g(s)))}},findSnapForVertex:function(t,e){for(var n=0;n<e.length;n++){if(t.equals2D(e[n]))return null;if(t.distance(e[n])<this.snapTolerance)return e[n]}return null},snapTo:function(t){var e=new N(this.srcPts);this.snapVertices(e,t),this.snapSegments(e,t);var n=e.toCoordinateArray();return n},snapSegments:function(t,e){if(0===e.length)return null;var n=e.length;e[0].equals2D(e[e.length-1])&&(n=e.length-1);for(var i=0;n>i;i++){var r=e[i],s=this.findSegmentIndexToSnap(r,t);s>=0&&t.add(s+1,new g(r),!1)}},findSegmentIndexToSnap:function(t,e){for(var n=r.MAX_VALUE,i=-1,s=0;s<e.size()-1;s++){if(this.seg.p0=e.get(s),this.seg.p1=e.get(s+1),this.seg.p0.equals2D(t)||this.seg.p1.equals2D(t)){if(this.allowSnappingToSourceVertices)continue;return-1}var o=this.seg.distance(t);o<this.snapTolerance&&n>o&&(n=o,i=s)}return i},setAllowSnappingToSourceVertices:function(t){this.allowSnappingToSourceVertices=t},interfaces_:function(){return[]},getClass:function(){return Ee}}),Ee.isClosed=function(t){return t.length<=1?!1:t[0].equals2D(t[t.length-1])},e(Ie.prototype,{snapTo:function(t,e){var n=this.extractTargetCoordinates(t),i=new Ne(e,n);return i.transform(this.srcGeom)},snapToSelf:function(t,e){var n=this.extractTargetCoordinates(this.srcGeom),i=new Ne(t,n,!0),r=i.transform(this.srcGeom),s=r;return e&&R(s,Rt)&&(s=r.buffer(0)),s},computeSnapTolerance:function(t){var e=this.computeMinimumSegmentLength(t),n=e/10;return n},extractTargetCoordinates:function(t){for(var e=new at,n=t.getCoordinates(),i=0;i<n.length;i++)e.add(n[i]);return e.toArray(new Array(0).fill(null))},computeMinimumSegmentLength:function(t){for(var e=r.MAX_VALUE,n=0;n<t.length-1;n++){var i=t[n].distance(t[n+1]);e>i&&(e=i)}return e},interfaces_:function(){return[]},getClass:function(){return Ie}}),Ie.snap=function(t,e,n){var i=new Array(2).fill(null),r=new Ie(t);i[0]=r.snapTo(e,n);var s=new Ie(e);return i[1]=s.snapTo(i[0],n),i},Ie.computeOverlaySnapTolerance=function(){if(1===arguments.length){var t=arguments[0],e=Ie.computeSizeBasedSnapTolerance(t),n=t.getPrecisionModel();if(n.getType()===ee.FIXED){var i=1/n.getScale()*2/1.415;i>e&&(e=i)}return e}if(2===arguments.length){var r=arguments[0],s=arguments[1];return Math.min(Ie.computeOverlaySnapTolerance(r),Ie.computeOverlaySnapTolerance(s))}},Ie.computeSizeBasedSnapTolerance=function(t){var e=t.getEnvelopeInternal(),n=Math.min(e.getHeight(),e.getWidth()),i=n*Ie.SNAP_PRECISION_FACTOR;return i},Ie.snapToSelf=function(t,e,n){var i=new Ie(t);return i.snapToSelf(e,n)},Ie.SNAP_PRECISION_FACTOR=1e-9,h(Ne,xe),e(Ne.prototype,{snapLine:function(t,e){var n=new Ee(t,this.snapTolerance);return n.setAllowSnappingToSourceVertices(this.isSelfSnap),n.snapTo(e)},transformCoordinates:function(t,e){var n=t.toCoordinateArray(),i=this.snapLine(n,this.snapPts);return this.factory.getCoordinateSequenceFactory().create(i)},interfaces_:function(){return[]},getClass:function(){return Ne}}),e(Ce.prototype,{getCommon:function(){return r.longBitsToDouble(this.commonBits)},add:function(t){var e=r.doubleToLongBits(t);if(this.isFirst)return this.commonBits=e,this.commonSignExp=Ce.signExpBits(this.commonBits),this.isFirst=!1,null;var n=Ce.signExpBits(e);return n!==this.commonSignExp?(this.commonBits=0,null):(this.commonMantissaBitsCount=Ce.numCommonMostSigMantissaBits(this.commonBits,e),void(this.commonBits=Ce.zeroLowerBits(this.commonBits,64-(12+this.commonMantissaBitsCount))))},toString:function(){if(1===arguments.length){var t=arguments[0],e=r.longBitsToDouble(t),n=Long.toBinaryString(t),i="0000000000000000000000000000000000000000000000000000000000000000"+n,s=i.substring(i.length-64),o=s.substring(0,1)+"  "+s.substring(1,12)+"(exp) "+s.substring(12)+" [ "+e+" ]";return o}},interfaces_:function(){return[]},getClass:function(){return Ce}}),Ce.getBit=function(t,e){var n=1<<e;return 0!==(t&n)?1:0},Ce.signExpBits=function(t){return t>>52},Ce.zeroLowerBits=function(t,e){var n=(1<<e)-1,i=~n,r=t&i;return r},Ce.numCommonMostSigMantissaBits=function(t,e){for(var n=0,i=52;i>=0;i--){if(Ce.getBit(t,i)!==Ce.getBit(e,i))return n;n++}return 52},e(Se.prototype,{addCommonBits:function(t){var e=new Le(this.commonCoord);t.apply(e),t.geometryChanged()},removeCommonBits:function(t){if(0===this.commonCoord.x&&0===this.commonCoord.y)return t;var e=new g(this.commonCoord);e.x=-e.x,e.y=-e.y;var n=new Le(e);return t.apply(n),t.geometryChanged(),t},getCommonCoordinate:function(){return this.commonCoord},add:function(t){t.apply(this.ccFilter),this.commonCoord=this.ccFilter.getCommonCoordinate()},interfaces_:function(){return[]},getClass:function(){return Se}}),e(we.prototype,{filter:function(t){this.commonBitsX.add(t.x),this.commonBitsY.add(t.y)},getCommonCoordinate:function(){return new g(this.commonBitsX.getCommon(),this.commonBitsY.getCommon())},interfaces_:function(){return[z]},getClass:function(){return we}}),e(Le.prototype,{filter:function(t,e){var n=t.getOrdinate(e,0)+this.trans.x,i=t.getOrdinate(e,1)+this.trans.y;t.setOrdinate(e,0,n),t.setOrdinate(e,1,i)},isDone:function(){return!1},isGeometryChanged:function(){return!0},interfaces_:function(){return[ct]},getClass:function(){return Le}}),Se.CommonCoordinateFilter=we,Se.Translater=Le,e(Re.prototype,{next:function(){if(this.atStart)return this.atStart=!1,Re.isAtomic(this.parent)&&this.index++,this.parent;if(null!==this.subcollectionIterator){if(this.subcollectionIterator.hasNext())return this.subcollectionIterator.next();this.subcollectionIterator=null}if(this.index>=this.max)throw new x;var t=this.parent.getGeometryN(this.index++);return t instanceof ft?(this.subcollectionIterator=new Re(t),this.subcollectionIterator.next()):t},remove:function(){throw new UnsupportedOperationException(this.getClass().getName())},hasNext:function(){if(this.atStart)return!0;if(null!==this.subcollectionIterator){if(this.subcollectionIterator.hasNext())return!0;this.subcollectionIterator=null}return!(this.index>=this.max)},interfaces_:function(){return[p]},getClass:function(){return Re}}),Re.isAtomic=function(t){return!(t instanceof ft)},e(Te.prototype,{locateInternal:function(){if(arguments[0]instanceof g&&arguments[1]instanceof Tt){var t=arguments[0],e=arguments[1];if(e.isEmpty())return L.EXTERIOR;var n=e.getExteriorRing(),i=this.locateInPolygonRing(t,n);if(i===L.EXTERIOR)return L.EXTERIOR;if(i===L.BOUNDARY)return L.BOUNDARY;for(var r=0;r<e.getNumInteriorRing();r++){var s=e.getInteriorRingN(r),o=this.locateInPolygonRing(t,s);if(o===L.INTERIOR)return L.EXTERIOR;if(o===L.BOUNDARY)return L.BOUNDARY}return L.INTERIOR}if(arguments[0]instanceof g&&arguments[1]instanceof St){var a=arguments[0],u=arguments[1];if(!u.getEnvelopeInternal().intersects(a))return L.EXTERIOR;var l=u.getCoordinates();return u.isClosed()||!a.equals(l[0])&&!a.equals(l[l.length-1])?he.isOnLine(a,l)?L.INTERIOR:L.EXTERIOR:L.BOUNDARY}if(arguments[0]instanceof g&&arguments[1]instanceof Lt){var h=arguments[0],c=arguments[1],f=c.getCoordinate();return f.equals2D(h)?L.INTERIOR:L.EXTERIOR}},locateInPolygonRing:function(t,e){return e.getEnvelopeInternal().intersects(t)?he.locatePointInRing(t,e.getCoordinates()):L.EXTERIOR},intersects:function(t,e){return this.locate(t,e)!==L.EXTERIOR},updateLocationInfo:function(t){t===L.INTERIOR&&(this.isIn=!0),t===L.BOUNDARY&&this.numBoundaries++},computeLocation:function(t,e){if(e instanceof Lt&&this.updateLocationInfo(this.locateInternal(t,e)),e instanceof St)this.updateLocationInfo(this.locateInternal(t,e));else if(e instanceof Tt)this.updateLocationInfo(this.locateInternal(t,e));else if(e instanceof gt)for(var n=e,i=0;i<n.getNumGeometries();i++){var r=n.getGeometryN(i);this.updateLocationInfo(this.locateInternal(t,r))}else if(e instanceof Ot)for(var s=e,i=0;i<s.getNumGeometries();i++){var o=s.getGeometryN(i);this.updateLocationInfo(this.locateInternal(t,o))}else if(e instanceof ft)for(var a=new Re(e);a.hasNext();){var u=a.next();u!==e&&this.computeLocation(t,u)}},locate:function(t,e){return e.isEmpty()?L.EXTERIOR:e instanceof St?this.locateInternal(t,e):e instanceof Tt?this.locateInternal(t,e):(this.isIn=!1,this.numBoundaries=0,this.computeLocation(t,e),this.boundaryRule.isInBoundary(this.numBoundaries)?L.BOUNDARY:this.numBoundaries>0||this.isIn?L.INTERIOR:L.EXTERIOR)},interfaces_:function(){return[]},getClass:function(){return Te}}),e(Pe.prototype,{interfaces_:function(){return[]},getClass:function(){return Pe}}),Pe.octant=function(){if("number"==typeof arguments[0]&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1];if(0===t&&0===e)throw new i("Cannot compute the octant for point ( "+t+", "+e+" )");var n=Math.abs(t),r=Math.abs(e);return t>=0?e>=0?n>=r?0:1:n>=r?7:6:e>=0?n>=r?3:2:n>=r?4:5}if(arguments[0]instanceof g&&arguments[1]instanceof g){var s=arguments[0],o=arguments[1],a=o.x-s.x,u=o.y-s.y;if(0===a&&0===u)throw new i("Cannot compute the octant for two identical points "+s);return Pe.octant(a,u)}},e(be.prototype,{getCoordinates:function(){},size:function(){},getCoordinate:function(t){},isClosed:function(){},setData:function(t){},getData:function(){},interfaces_:function(){return[]},getClass:function(){return be}}),e(Oe.prototype,{getCoordinates:function(){return this.pts},size:function(){return this.pts.length},getCoordinate:function(t){return this.pts[t]},isClosed:function(){return this.pts[0].equals(this.pts[this.pts.length-1])},getSegmentOctant:function(t){return t===this.pts.length-1?-1:Pe.octant(this.getCoordinate(t),this.getCoordinate(t+1))},setData:function(t){this.data=t},getData:function(){return this.data},toString:function(){return se.toLineString(new Gt(this.pts))},interfaces_:function(){return[be]},getClass:function(){return Oe}}),e(_e.prototype,{getBounds:function(){},interfaces_:function(){return[]},getClass:function(){return _e}}),e(Me.prototype,{getItem:function(){return this.item},getBounds:function(){return this.bounds},interfaces_:function(){return[_e,u]},getClass:function(){return Me}}),e(De.prototype,{poll:function(){if(this.isEmpty())return null;var t=this.items.get(1);return this.items.set(1,this.items.get(this._size)),this._size-=1,this.reorder(1),t},size:function(){return this._size},reorder:function(t){for(var e=null,n=this.items.get(t);2*t<=this._size&&(e=2*t,e!==this._size&&this.items.get(e+1).compareTo(this.items.get(e))<0&&e++,this.items.get(e).compareTo(n)<0);t=e)this.items.set(t,this.items.get(e));this.items.set(t,n)},clear:function(){this._size=0,this.items.clear()},isEmpty:function(){return 0===this._size},add:function(t){this.items.add(null),this._size+=1;var e=this._size;for(this.items.set(0,t);t.compareTo(this.items.get(Math.trunc(e/2)))<0;e/=2)this.items.set(e,this.items.get(Math.trunc(e/2)));this.items.set(e,t)},interfaces_:function(){return[]},getClass:function(){return De}}),e(Ae.prototype,{visitItem:function(t){},interfaces_:function(){return[]},getClass:function(){return Ae}}),e(Fe.prototype,{insert:function(t,e){},remove:function(t,e){},query:function(){if(1===arguments.length){arguments[0]}else if(2===arguments.length){arguments[0],arguments[1]}},interfaces_:function(){return[]},getClass:function(){return Fe}}),e(Ge.prototype,{getLevel:function(){return this.level},size:function(){return this.childBoundables.size()},getChildBoundables:function(){return this.childBoundables},addChildBoundable:function(t){f.isTrue(null===this.bounds),this.childBoundables.add(t)},isEmpty:function(){return this.childBoundables.isEmpty()},getBounds:function(){return null===this.bounds&&(this.bounds=this.computeBounds()),this.bounds},interfaces_:function(){return[_e,u]},getClass:function(){return Ge}}),Ge.serialVersionUID=0x5a1e55ec41369800;var ho={reverseOrder:function(){return{compare:function(t,e){return e.compareTo(t)}}},min:function(t){return ho.sort(t),t.get(0)},sort:function(t,e){var n=t.toArray();e?ut.sort(n,e):ut.sort(n);for(var i=t.iterator(),r=0,s=n.length;s>r;r++)i.next(),i.set(n[r])},singletonList:function(t){var e=new I;return e.add(t),e}};e(qe.prototype,{expandToQueue:function(t,e){var n=qe.isComposite(this.boundable1),r=qe.isComposite(this.boundable2);if(n&&r)return qe.area(this.boundable1)>qe.area(this.boundable2)?(this.expand(this.boundable1,this.boundable2,t,e),null):(this.expand(this.boundable2,this.boundable1,t,e),null);if(n)return this.expand(this.boundable1,this.boundable2,t,e),null;if(r)return this.expand(this.boundable2,this.boundable1,t,e),null;throw new i("neither boundable is composite")},isLeaves:function(){return!(qe.isComposite(this.boundable1)||qe.isComposite(this.boundable2))},compareTo:function(t){var e=t;return this._distance<e._distance?-1:this._distance>e._distance?1:0},expand:function(t,e,n,i){for(var r=t.getChildBoundables(),s=r.iterator();s.hasNext();){var o=s.next(),a=new qe(o,e,this.itemDistance);a.getDistance()<i&&n.add(a)}},getBoundable:function(t){return 0===t?this.boundable1:this.boundable2},getDistance:function(){return this._distance},distance:function(){return this.isLeaves()?this.itemDistance.distance(this.boundable1,this.boundable2):this.boundable1.getBounds().distance(this.boundable2.getBounds())},interfaces_:function(){return[s]},getClass:function(){return qe}}),qe.area=function(t){return t.getBounds().getArea()},qe.isComposite=function(t){return t instanceof Ge},e(Be.prototype,{getNodeCapacity:function(){return this.nodeCapacity},lastNode:function(t){return t.get(t.size()-1)},size:function t(){if(0===arguments.length)return this.isEmpty()?0:(this.build(),this.size(this.root));if(1===arguments.length){for(var e=arguments[0],t=0,n=e.getChildBoundables().iterator();n.hasNext();){var i=n.next();i instanceof Ge?t+=this.size(i):i instanceof Me&&(t+=1)}return t}},removeItem:function(t,e){for(var n=null,i=t.getChildBoundables().iterator();i.hasNext();){var r=i.next();r instanceof Me&&r.getItem()===e&&(n=r)}return null!==n?(t.getChildBoundables().remove(n),!0):!1},itemsTree:function(){if(0===arguments.length){this.build();var t=this.itemsTree(this.root);return null===t?new I:t}if(1===arguments.length){for(var e=arguments[0],n=new I,i=e.getChildBoundables().iterator();i.hasNext();){var r=i.next();if(r instanceof Ge){var s=this.itemsTree(r);null!==s&&n.add(s)}else r instanceof Me?n.add(r.getItem()):f.shouldNeverReachHere()}return n.size()<=0?null:n}},insert:function(t,e){f.isTrue(!this.built,"Cannot insert items into an STR packed R-tree after it has been built."),this.itemBoundables.add(new Me(t,e))},boundablesAtLevel:function(){if(1===arguments.length){var t=arguments[0],e=new I;return this.boundablesAtLevel(t,this.root,e),e}if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];if(f.isTrue(n>-2),i.getLevel()===n)return r.add(i),null;for(var s=i.getChildBoundables().iterator();s.hasNext();){var o=s.next();o instanceof Ge?this.boundablesAtLevel(n,o,r):(f.isTrue(o instanceof Me),-1===n&&r.add(o))}return null}},query:function(){if(1===arguments.length){var t=arguments[0];this.build();var e=new I;return this.isEmpty()?e:(this.getIntersectsOp().intersects(this.root.getBounds(),t)&&this.query(t,this.root,e),e)}if(2===arguments.length){var n=arguments[0],i=arguments[1];if(this.build(),this.isEmpty())return null;this.getIntersectsOp().intersects(this.root.getBounds(),n)&&this.query(n,this.root,i)}else if(3===arguments.length)if(R(arguments[2],Ae)&&arguments[0]instanceof Object&&arguments[1]instanceof Ge)for(var r=arguments[0],s=arguments[1],o=arguments[2],a=s.getChildBoundables(),u=0;u<a.size();u++){var l=a.get(u);this.getIntersectsOp().intersects(l.getBounds(),r)&&(l instanceof Ge?this.query(r,l,o):l instanceof Me?o.visitItem(l.getItem()):f.shouldNeverReachHere())}else if(R(arguments[2],y)&&arguments[0]instanceof Object&&arguments[1]instanceof Ge)for(var h=arguments[0],c=arguments[1],g=arguments[2],a=c.getChildBoundables(),u=0;u<a.size();u++){var l=a.get(u);this.getIntersectsOp().intersects(l.getBounds(),h)&&(l instanceof Ge?this.query(h,l,g):l instanceof Me?g.add(l.getItem()):f.shouldNeverReachHere())}},build:function(){return this.built?null:(this.root=this.itemBoundables.isEmpty()?this.createNode(0):this.createHigherLevels(this.itemBoundables,-1),this.itemBoundables=null,void(this.built=!0))},getRoot:function(){return this.build(),this.root},remove:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.build(),this.getIntersectsOp().intersects(this.root.getBounds(),t)?this.remove(t,this.root,e):!1}if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2],s=this.removeItem(i,r);if(s)return!0;for(var o=null,a=i.getChildBoundables().iterator();a.hasNext();){var u=a.next();if(this.getIntersectsOp().intersects(u.getBounds(),n)&&u instanceof Ge&&(s=this.remove(n,u,r))){o=u;break}}return null!==o&&o.getChildBoundables().isEmpty()&&i.getChildBoundables().remove(o),s}},createHigherLevels:function(t,e){f.isTrue(!t.isEmpty());var n=this.createParentBoundables(t,e+1);return 1===n.size()?n.get(0):this.createHigherLevels(n,e+1)},depth:function(){if(0===arguments.length)return this.isEmpty()?0:(this.build(),this.depth(this.root));if(1===arguments.length){for(var t=arguments[0],e=0,n=t.getChildBoundables().iterator();n.hasNext();){var i=n.next();if(i instanceof Ge){var r=this.depth(i);r>e&&(e=r)}}return e+1}},createParentBoundables:function(t,e){f.isTrue(!t.isEmpty());var n=new I;n.add(this.createNode(e));var i=new I(t);ho.sort(i,this.getComparator());for(var r=i.iterator();r.hasNext();){var s=r.next();this.lastNode(n).getChildBoundables().size()===this.getNodeCapacity()&&n.add(this.createNode(e)),this.lastNode(n).addChildBoundable(s)}return n},isEmpty:function(){return this.built?this.root.isEmpty():this.itemBoundables.isEmpty()},interfaces_:function(){return[u]},getClass:function(){return Be}}),Be.compareDoubles=function(t,e){return t>e?1:e>t?-1:0},Be.IntersectsOp=ze,Be.serialVersionUID=-0x35ef64c82d4c5400,Be.DEFAULT_NODE_CAPACITY=10,e(Ve.prototype,{distance:function(t,e){},interfaces_:function(){return[]},getClass:function(){return Ve}}),h(ke,Be),e(ke.prototype,{createParentBoundablesFromVerticalSlices:function(t,e){f.isTrue(t.length>0);for(var n=new I,i=0;i<t.length;i++)n.addAll(this.createParentBoundablesFromVerticalSlice(t[i],e));return n},createNode:function(t){return new Ye(t)},size:function(){return 0===arguments.length?Be.prototype.size.call(this):Be.prototype.size.apply(this,arguments)},insert:function(){if(2!==arguments.length)return Be.prototype.insert.apply(this,arguments);var t=arguments[0],e=arguments[1];return t.isNull()?null:void Be.prototype.insert.call(this,t,e)},getIntersectsOp:function(){return ke.intersectsOp},verticalSlices:function(t,e){for(var n=Math.trunc(Math.ceil(t.size()/e)),i=new Array(e).fill(null),r=t.iterator(),s=0;e>s;s++){i[s]=new I;for(var o=0;r.hasNext()&&n>o;){var a=r.next();i[s].add(a),o++}}return i},query:function(){if(1===arguments.length){var t=arguments[0];return Be.prototype.query.call(this,t)}if(2===arguments.length){var e=arguments[0],n=arguments[1];Be.prototype.query.call(this,e,n)}else if(3===arguments.length)if(R(arguments[2],Ae)&&arguments[0]instanceof Object&&arguments[1]instanceof Ge){var i=arguments[0],r=arguments[1],s=arguments[2];Be.prototype.query.call(this,i,r,s)}else if(R(arguments[2],y)&&arguments[0]instanceof Object&&arguments[1]instanceof Ge){var o=arguments[0],a=arguments[1],u=arguments[2];Be.prototype.query.call(this,o,a,u)}},getComparator:function(){return ke.yComparator},createParentBoundablesFromVerticalSlice:function(t,e){return Be.prototype.createParentBoundables.call(this,t,e)},remove:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return Be.prototype.remove.call(this,t,e)}return Be.prototype.remove.apply(this,arguments)},depth:function(){return 0===arguments.length?Be.prototype.depth.call(this):Be.prototype.depth.apply(this,arguments)},createParentBoundables:function(t,e){f.isTrue(!t.isEmpty());var n=Math.trunc(Math.ceil(t.size()/this.getNodeCapacity())),i=new I(t);ho.sort(i,ke.xComparator);var r=this.verticalSlices(i,Math.trunc(Math.ceil(Math.sqrt(n))));return this.createParentBoundablesFromVerticalSlices(r,e)},nearestNeighbour:function(){if(1===arguments.length){if(R(arguments[0],Ve)){var t=arguments[0],e=new qe(this.getRoot(),this.getRoot(),t);return this.nearestNeighbour(e)}if(arguments[0]instanceof qe){var n=arguments[0];return this.nearestNeighbour(n,r.POSITIVE_INFINITY)}}else if(2===arguments.length){if(arguments[0]instanceof ke&&R(arguments[1],Ve)){var i=arguments[0],s=arguments[1],e=new qe(this.getRoot(),i.getRoot(),s);return this.nearestNeighbour(e)}if(arguments[0]instanceof qe&&"number"==typeof arguments[1]){var o=arguments[0],a=arguments[1],u=a,l=null,h=new De;for(h.add(o);!h.isEmpty()&&u>0;){var c=h.poll(),f=c.getDistance();if(f>=u)break;c.isLeaves()?(u=f,l=c):c.expandToQueue(h,u)}return[l.getBoundable(0).getItem(),l.getBoundable(1).getItem()]}}else if(3===arguments.length){var g=arguments[0],d=arguments[1],p=arguments[2],v=new Me(g,d),e=new qe(this.getRoot(),v,p);return this.nearestNeighbour(e)[0]}},interfaces_:function(){return[Fe,u]},getClass:function(){return ke}}),ke.centreX=function(t){return ke.avg(t.getMinX(),t.getMaxX())},ke.avg=function(t,e){return(t+e)/2},ke.centreY=function(t){return ke.avg(t.getMinY(),t.getMaxY())},h(Ye,Ge),e(Ye.prototype,{computeBounds:function(){for(var t=null,e=this.getChildBoundables().iterator();e.hasNext();){var n=e.next();null===t?t=new C(n.getBounds()):t.expandToInclude(n.getBounds())}return t},interfaces_:function(){return[]},getClass:function(){return Ye}}),ke.STRtreeNode=Ye,ke.serialVersionUID=0x39920f7d5f261e0,ke.xComparator={interfaces_:function(){return[a]},compare:function(t,e){return Be.compareDoubles(ke.centreX(t.getBounds()),ke.centreX(e.getBounds()))}},ke.yComparator={interfaces_:function(){return[a]},compare:function(t,e){return Be.compareDoubles(ke.centreY(t.getBounds()),ke.centreY(e.getBounds()))}},ke.intersectsOp={interfaces_:function(){return[IntersectsOp]},intersects:function(t,e){return t.intersects(e)}},ke.DEFAULT_NODE_CAPACITY=10,e(Ue.prototype,{interfaces_:function(){return[]},getClass:function(){return Ue}}),Ue.relativeSign=function(t,e){return e>t?-1:t>e?1:0},Ue.compare=function(t,e,n){if(e.equals2D(n))return 0;var i=Ue.relativeSign(e.x,n.x),r=Ue.relativeSign(e.y,n.y);switch(t){case 0:return Ue.compareValue(i,r);case 1:return Ue.compareValue(r,i);case 2:return Ue.compareValue(r,-i);case 3:return Ue.compareValue(-i,r);case 4:return Ue.compareValue(-i,-r);case 5:return Ue.compareValue(-r,-i);case 6:return Ue.compareValue(-r,i);case 7:return Ue.compareValue(i,-r)}return f.shouldNeverReachHere("invalid octant value"),0},Ue.compareValue=function(t,e){return 0>t?-1:t>0?1:0>e?-1:e>0?1:0},e(Xe.prototype,{getCoordinate:function(){return this.coord},print:function(t){t.print(this.coord),t.print(" seg # = "+this.segmentIndex)},compareTo:function(t){var e=t;return this.segmentIndex<e.segmentIndex?-1:this.segmentIndex>e.segmentIndex?1:this.coord.equals2D(e.coord)?0:Ue.compare(this.segmentOctant,this.coord,e.coord)},isEndPoint:function(t){return 0!==this.segmentIndex||this._isInterior?this.segmentIndex===t:!0},isInterior:function(){return this._isInterior},interfaces_:function(){return[s]},getClass:function(){return Xe}}),e(He.prototype,{getSplitCoordinates:function(){var t=new N;this.addEndpoints();for(var e=this.iterator(),n=e.next();e.hasNext();){var i=e.next();this.addEdgeCoordinates(n,i,t),n=i}return t.toCoordinateArray()},addCollapsedNodes:function(){var t=new I;this.findCollapsesFromInsertedNodes(t),this.findCollapsesFromExistingVertices(t);for(var e=t.iterator();e.hasNext();){var n=e.next().intValue();this.add(this.edge.getCoordinate(n),n)}},print:function(t){t.println("Intersections:");for(var e=this.iterator();e.hasNext();){var n=e.next();n.print(t)}},findCollapsesFromExistingVertices:function(t){for(var e=0;e<this.edge.size()-2;e++){var n=this.edge.getCoordinate(e),i=(this.edge.getCoordinate(e+1),this.edge.getCoordinate(e+2));n.equals2D(i)&&t.add(new b(e+1))}},addEdgeCoordinates:function(t,e,n){var i=e.segmentIndex-t.segmentIndex+2,r=this.edge.getCoordinate(e.segmentIndex),s=e.isInterior()||!e.coord.equals2D(r);s||i--;n.add(new g(t.coord),!1);for(var o=t.segmentIndex+1;o<=e.segmentIndex;o++)n.add(this.edge.getCoordinate(o));s&&n.add(new g(e.coord))},iterator:function(){return this.nodeMap.values().iterator()},addSplitEdges:function(t){this.addEndpoints(),this.addCollapsedNodes();for(var e=this.iterator(),n=e.next();e.hasNext();){var i=e.next(),r=this.createSplitEdge(n,i);t.add(r),n=i}},findCollapseIndex:function(t,e,n){if(!t.coord.equals2D(e.coord))return!1;var i=e.segmentIndex-t.segmentIndex;return e.isInterior()||i--,1===i?(n[0]=t.segmentIndex+1,!0):!1},findCollapsesFromInsertedNodes:function(t){for(var e=new Array(1).fill(null),n=this.iterator(),i=n.next();n.hasNext();){var r=n.next(),s=this.findCollapseIndex(i,r,e);s&&t.add(new b(e[0])),i=r}},getEdge:function(){return this.edge},addEndpoints:function(){var t=this.edge.size()-1;this.add(this.edge.getCoordinate(0),0),this.add(this.edge.getCoordinate(t),t)},createSplitEdge:function(t,e){var n=e.segmentIndex-t.segmentIndex+2,i=this.edge.getCoordinate(e.segmentIndex),r=e.isInterior()||!e.coord.equals2D(i);r||n--;var s=new Array(n).fill(null),o=0;s[o++]=new g(t.coord);for(var a=t.segmentIndex+1;a<=e.segmentIndex;a++)s[o++]=this.edge.getCoordinate(a);return r&&(s[o]=new g(e.coord)),new Ke(s,this.edge.getData())},add:function(t,e){var n=new Xe(this.edge,t,e,this.edge.getSegmentOctant(e)),i=this.nodeMap.get(n);return null!==i?(f.isTrue(i.coord.equals2D(t),"Found equal nodes with different coordinates"),i):(this.nodeMap.put(n,n),n)},checkSplitEdgesCorrectness:function(t){var e=this.edge.getCoordinates(),n=t.get(0),i=n.getCoordinate(0);if(!i.equals2D(e[0]))throw new l("bad split edge start point at "+i);var r=t.get(t.size()-1),s=r.getCoordinates(),o=s[s.length-1];if(!o.equals2D(e[e.length-1]))throw new l("bad split edge end point at "+o)},interfaces_:function(){return[]},getClass:function(){return He}}),e(We.prototype,{next:function(){return null===this.currNode?(this.currNode=this.nextNode,this.currSegIndex=this.currNode.segmentIndex,this.readNextNode(),this.currNode):null===this.nextNode?null:this.nextNode.segmentIndex===this.currNode.segmentIndex?(this.currNode=this.nextNode,this.currSegIndex=this.currNode.segmentIndex,this.readNextNode(),this.currNode):(this.nextNode.segmentIndex>this.currNode.segmentIndex,null)},remove:function(){throw new UnsupportedOperationException(this.getClass().getName())},hasNext:function(){return null!==this.nextNode},readNextNode:function(){this.nodeIt.hasNext()?this.nextNode=this.nodeIt.next():this.nextNode=null},interfaces_:function(){return[p]},getClass:function(){return We}}),e(je.prototype,{addIntersection:function(t,e){},interfaces_:function(){return[be]},getClass:function(){return je}}),e(Ke.prototype,{getCoordinates:function(){return this.pts},size:function(){return this.pts.length},getCoordinate:function(t){return this.pts[t]},isClosed:function(){return this.pts[0].equals(this.pts[this.pts.length-1])},getSegmentOctant:function(t){return t===this.pts.length-1?-1:this.safeOctant(this.getCoordinate(t),this.getCoordinate(t+1))},setData:function(t){this.data=t},safeOctant:function(t,e){return t.equals2D(e)?0:Pe.octant(t,e)},getData:function(){return this.data},addIntersection:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];this.addIntersectionNode(t,e)}else if(4===arguments.length){var n=arguments[0],i=arguments[1],r=(arguments[2],arguments[3]),s=new g(n.getIntersection(r));this.addIntersection(s,i)}},toString:function(){return se.toLineString(new Gt(this.pts))},getNodeList:function(){return this.nodeList},addIntersectionNode:function(t,e){var n=e,i=n+1;if(i<this.pts.length){var r=this.pts[i];t.equals2D(r)&&(n=i)}var s=this.nodeList.add(t,n);return s},addIntersections:function(t,e,n){for(var i=0;i<t.getIntersectionNum();i++)this.addIntersection(t,e,n,i)},interfaces_:function(){return[je]},getClass:function(){return Ke}}),Ke.getNodedSubstrings=function(){if(1===arguments.length){var t=arguments[0],e=new I;return Ke.getNodedSubstrings(t,e),e}if(2===arguments.length)for(var n=arguments[0],i=arguments[1],r=n.iterator();r.hasNext();){var s=r.next();s.getNodeList().addSplitEdges(i)}},e(Ze.prototype,{overlap:function(){if(2===arguments.length){arguments[0],arguments[1]}else if(4===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];t.getLineSegment(e,this.overlapSeg1),n.getLineSegment(i,this.overlapSeg2),this.overlap(this.overlapSeg1,this.overlapSeg2)}},interfaces_:function(){return[]},getClass:function(){return Ze}}),e(Qe.prototype,{getLineSegment:function(t,e){e.p0=this.pts[t],e.p1=this.pts[t+1]},computeSelect:function(t,e,n,i){var r=this.pts[e],s=this.pts[n];if(i.tempEnv1.init(r,s),n-e===1)return i.select(this,e),null;if(!t.intersects(i.tempEnv1))return null;var o=Math.trunc((e+n)/2);
o>e&&this.computeSelect(t,e,o,i),n>o&&this.computeSelect(t,o,n,i)},getCoordinates:function(){for(var t=new Array(this.end-this.start+1).fill(null),e=0,n=this.start;n<=this.end;n++)t[e++]=this.pts[n];return t},computeOverlaps:function(t,e){this.computeOverlapsInternal(this.start,this.end,t,t.start,t.end,e)},setId:function(t){this.id=t},select:function(t,e){this.computeSelect(t,this.start,this.end,e)},getEnvelope:function(){if(null===this.env){var t=this.pts[this.start],e=this.pts[this.end];this.env=new C(t,e)}return this.env},getEndIndex:function(){return this.end},getStartIndex:function(){return this.start},getContext:function(){return this.context},getId:function(){return this.id},computeOverlapsInternal:function(t,e,n,i,r,s){var o=this.pts[t],a=this.pts[e],u=n.pts[i],l=n.pts[r];if(e-t===1&&r-i===1)return s.overlap(this,t,n,i),null;if(s.tempEnv1.init(o,a),s.tempEnv2.init(u,l),!s.tempEnv1.intersects(s.tempEnv2))return null;var h=Math.trunc((t+e)/2),c=Math.trunc((i+r)/2);h>t&&(c>i&&this.computeOverlapsInternal(t,h,n,i,c,s),r>c&&this.computeOverlapsInternal(t,h,n,c,r,s)),e>h&&(c>i&&this.computeOverlapsInternal(h,e,n,i,c,s),r>c&&this.computeOverlapsInternal(h,e,n,c,r,s))},interfaces_:function(){return[]},getClass:function(){return Qe}}),e(Je.prototype,{interfaces_:function(){return[]},getClass:function(){return Je}}),Je.isNorthern=function(t){return t===Je.NE||t===Je.NW},Je.isOpposite=function(t,e){if(t===e)return!1;var n=(t-e+4)%4;return 2===n},Je.commonHalfPlane=function(t,e){if(t===e)return t;var n=(t-e+4)%4;if(2===n)return-1;var i=e>t?t:e,r=t>e?t:e;return 0===i&&3===r?3:i},Je.isInHalfPlane=function(t,e){return e===Je.SE?t===Je.SE||t===Je.SW:t===e||t===e+1},Je.quadrant=function(){if("number"==typeof arguments[0]&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1];if(0===t&&0===e)throw new i("Cannot compute the quadrant for point ( "+t+", "+e+" )");return t>=0?e>=0?Je.NE:Je.SE:e>=0?Je.NW:Je.SW}if(arguments[0]instanceof g&&arguments[1]instanceof g){var n=arguments[0],r=arguments[1];if(r.x===n.x&&r.y===n.y)throw new i("Cannot compute the quadrant for two identical points "+n);return r.x>=n.x?r.y>=n.y?Je.NE:Je.SE:r.y>=n.y?Je.NW:Je.SW}},Je.NE=0,Je.NW=1,Je.SW=2,Je.SE=3,e($e.prototype,{interfaces_:function(){return[]},getClass:function(){return $e}}),$e.getChainStartIndices=function(t){var e=0,n=new I;n.add(new b(e));do{var i=$e.findChainEnd(t,e);n.add(new b(i)),e=i}while(e<t.length-1);var r=$e.toIntArray(n);return r},$e.findChainEnd=function(t,e){for(var n=e;n<t.length-1&&t[n].equals2D(t[n+1]);)n++;if(n>=t.length-1)return t.length-1;for(var i=Je.quadrant(t[n],t[n+1]),r=e+1;r<t.length;){if(!t[r-1].equals2D(t[r])){var s=Je.quadrant(t[r-1],t[r]);if(s!==i)break}r++}return r-1},$e.getChains=function(){if(1===arguments.length){var t=arguments[0];return $e.getChains(t,null)}if(2===arguments.length){for(var e=arguments[0],n=arguments[1],i=new I,r=$e.getChainStartIndices(e),s=0;s<r.length-1;s++){var o=new Qe(e,r[s],r[s+1],n);i.add(o)}return i}},$e.toIntArray=function(t){for(var e=new Array(t.size()).fill(null),n=0;n<e.length;n++)e[n]=t.get(n).intValue();return e},e(tn.prototype,{computeNodes:function(t){},getNodedSubstrings:function(){},interfaces_:function(){return[]},getClass:function(){return tn}}),e(en.prototype,{setSegmentIntersector:function(t){this.segInt=t},interfaces_:function(){return[tn]},getClass:function(){return en}}),h(nn,en),e(nn.prototype,{getMonotoneChains:function(){return this.monoChains},getNodedSubstrings:function(){return Ke.getNodedSubstrings(this.nodedSegStrings)},getIndex:function(){return this.index},add:function(t){for(var e=$e.getChains(t.getCoordinates(),t),n=e.iterator();n.hasNext();){var i=n.next();i.setId(this.idCounter++),this.index.insert(i.getEnvelope(),i),this.monoChains.add(i)}},computeNodes:function(t){this.nodedSegStrings=t;for(var e=t.iterator();e.hasNext();)this.add(e.next());this.intersectChains()},intersectChains:function(){for(var t=new rn(this.segInt),e=this.monoChains.iterator();e.hasNext();)for(var n=e.next(),i=this.index.query(n.getEnvelope()),r=i.iterator();r.hasNext();){var s=r.next();if(s.getId()>n.getId()&&(n.computeOverlaps(s,t),this.nOverlaps++),this.segInt.isDone())return null}},interfaces_:function(){return[]},getClass:function(){return nn}}),h(rn,Ze),e(rn.prototype,{overlap:function(){if(4!==arguments.length)return Ze.prototype.overlap.apply(this,arguments);var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3],r=t.getContext(),s=n.getContext();this.si.processIntersections(r,e,s,i)},interfaces_:function(){return[]},getClass:function(){return rn}}),nn.SegmentOverlapAction=rn,h(sn,l),e(sn.prototype,{getCoordinate:function(){return this.pt},interfaces_:function(){return[]},getClass:function(){return sn}}),sn.msgWithCoord=function(t,e){return null!==e?t+" [ "+e+" ]":t},e(on.prototype,{processIntersections:function(t,e,n,i){},isDone:function(){},interfaces_:function(){return[]},getClass:function(){return on}}),e(an.prototype,{getInteriorIntersection:function(){return this.interiorIntersection},setCheckEndSegmentsOnly:function(t){this.isCheckEndSegmentsOnly=t},getIntersectionSegments:function(){return this.intSegments},count:function(){return this.intersectionCount},getIntersections:function(){return this.intersections},setFindAllIntersections:function(t){this.findAllIntersections=t},setKeepIntersections:function(t){this.keepIntersections=t},processIntersections:function(t,e,n,i){if(!this.findAllIntersections&&this.hasIntersection())return null;if(t===n&&e===i)return null;if(this.isCheckEndSegmentsOnly){var r=this.isEndSegment(t,e)||this.isEndSegment(n,i);if(!r)return null}var s=t.getCoordinates()[e],o=t.getCoordinates()[e+1],a=n.getCoordinates()[i],u=n.getCoordinates()[i+1];this.li.computeIntersection(s,o,a,u),this.li.hasIntersection()&&this.li.isInteriorIntersection()&&(this.intSegments=new Array(4).fill(null),this.intSegments[0]=s,this.intSegments[1]=o,this.intSegments[2]=a,this.intSegments[3]=u,this.interiorIntersection=this.li.getIntersection(0),this.keepIntersections&&this.intersections.add(this.interiorIntersection),this.intersectionCount++)},isEndSegment:function(t,e){return 0===e?!0:e>=t.size()-2},hasIntersection:function(){return null!==this.interiorIntersection},isDone:function(){return this.findAllIntersections?!1:null!==this.interiorIntersection},interfaces_:function(){return[on]},getClass:function(){return an}}),an.createAllIntersectionsFinder=function(t){var e=new an(t);return e.setFindAllIntersections(!0),e},an.createAnyIntersectionFinder=function(t){return new an(t)},an.createIntersectionCounter=function(t){var e=new an(t);return e.setFindAllIntersections(!0),e.setKeepIntersections(!1),e},e(un.prototype,{execute:function(){return null!==this.segInt?null:void this.checkInteriorIntersections()},getIntersections:function(){return this.segInt.getIntersections()},isValid:function(){return this.execute(),this._isValid},setFindAllIntersections:function(t){this.findAllIntersections=t},checkInteriorIntersections:function(){this._isValid=!0,this.segInt=new an(this.li),this.segInt.setFindAllIntersections(this.findAllIntersections);var t=new nn;return t.setSegmentIntersector(this.segInt),t.computeNodes(this.segStrings),this.segInt.hasIntersection()?(this._isValid=!1,null):void 0},checkValid:function(){if(this.execute(),!this._isValid)throw new sn(this.getErrorMessage(),this.segInt.getInteriorIntersection())},getErrorMessage:function(){if(this._isValid)return"no intersections found";var t=this.segInt.getIntersectionSegments();return"found non-noded intersection between "+se.toLineString(t[0],t[1])+" and "+se.toLineString(t[2],t[3])},interfaces_:function(){return[]},getClass:function(){return un}}),un.computeIntersections=function(t){var e=new un(t);return e.setFindAllIntersections(!0),e.isValid(),e.getIntersections()},e(ln.prototype,{checkValid:function(){this.nv.checkValid()},interfaces_:function(){return[]},getClass:function(){return ln}}),ln.toSegmentStrings=function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next();e.add(new Oe(i.getCoordinates(),i))}return e},ln.checkValid=function(t){var e=new ln(t);e.checkValid()},e(hn.prototype,{map:function(t){for(var e=new I,n=0;n<t.getNumGeometries();n++){var i=this.mapOp.map(t.getGeometryN(n));i.isEmpty()||e.add(i)}return t.getFactory().createGeometryCollection(ie.toGeometryArray(e))},interfaces_:function(){return[]},getClass:function(){return hn}}),hn.map=function(t,e){var n=new hn(e);return n.map(t)},e(cn.prototype,{interfaces_:function(){return[]},getClass:function(){return cn}}),cn.opposite=function(t){return t===cn.LEFT?cn.RIGHT:t===cn.RIGHT?cn.LEFT:t},cn.ON=0,cn.LEFT=1,cn.RIGHT=2,e(fn.prototype,{setAllLocations:function(t){for(var e=0;e<this.location.length;e++)this.location[e]=t},isNull:function(){for(var t=0;t<this.location.length;t++)if(this.location[t]!==L.NONE)return!1;return!0},setAllLocationsIfNull:function(t){for(var e=0;e<this.location.length;e++)this.location[e]===L.NONE&&(this.location[e]=t)},isLine:function(){return 1===this.location.length},merge:function(t){if(t.location.length>this.location.length){var e=new Array(3).fill(null);e[cn.ON]=this.location[cn.ON],e[cn.LEFT]=L.NONE,e[cn.RIGHT]=L.NONE,this.location=e}for(var n=0;n<this.location.length;n++)this.location[n]===L.NONE&&n<t.location.length&&(this.location[n]=t.location[n])},getLocations:function(){return this.location},flip:function(){if(this.location.length<=1)return null;var t=this.location[cn.LEFT];this.location[cn.LEFT]=this.location[cn.RIGHT],this.location[cn.RIGHT]=t},toString:function(){var t=new P;return this.location.length>1&&t.append(L.toLocationSymbol(this.location[cn.LEFT])),t.append(L.toLocationSymbol(this.location[cn.ON])),this.location.length>1&&t.append(L.toLocationSymbol(this.location[cn.RIGHT])),t.toString()},setLocations:function(t,e,n){this.location[cn.ON]=t,this.location[cn.LEFT]=e,this.location[cn.RIGHT]=n},get:function(t){return t<this.location.length?this.location[t]:L.NONE},isArea:function(){return this.location.length>1},isAnyNull:function(){for(var t=0;t<this.location.length;t++)if(this.location[t]===L.NONE)return!0;return!1},setLocation:function(){if(1===arguments.length){var t=arguments[0];this.setLocation(cn.ON,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.location[e]=n}},init:function(t){this.location=new Array(t).fill(null),this.setAllLocations(L.NONE)},isEqualOnSide:function(t,e){return this.location[e]===t.location[e]},allPositionsEqual:function(t){for(var e=0;e<this.location.length;e++)if(this.location[e]!==t)return!1;return!0},interfaces_:function(){return[]},getClass:function(){return fn}}),e(gn.prototype,{getGeometryCount:function(){var t=0;return this.elt[0].isNull()||t++,this.elt[1].isNull()||t++,t},setAllLocations:function(t,e){this.elt[t].setAllLocations(e)},isNull:function(t){return this.elt[t].isNull()},setAllLocationsIfNull:function(){if(1===arguments.length){var t=arguments[0];this.setAllLocationsIfNull(0,t),this.setAllLocationsIfNull(1,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.elt[e].setAllLocationsIfNull(n)}},isLine:function(t){return this.elt[t].isLine()},merge:function(t){for(var e=0;2>e;e++)null===this.elt[e]&&null!==t.elt[e]?this.elt[e]=new fn(t.elt[e]):this.elt[e].merge(t.elt[e])},flip:function(){this.elt[0].flip(),this.elt[1].flip()},getLocation:function(){if(1===arguments.length){var t=arguments[0];return this.elt[t].get(cn.ON)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return this.elt[e].get(n)}},toString:function(){var t=new P;return null!==this.elt[0]&&(t.append("A:"),t.append(this.elt[0].toString())),null!==this.elt[1]&&(t.append(" B:"),t.append(this.elt[1].toString())),t.toString()},isArea:function(){if(0===arguments.length)return this.elt[0].isArea()||this.elt[1].isArea();if(1===arguments.length){var t=arguments[0];return this.elt[t].isArea()}},isAnyNull:function(t){return this.elt[t].isAnyNull()},setLocation:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];this.elt[t].setLocation(cn.ON,e)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.elt[n].setLocation(i,r)}},isEqualOnSide:function(t,e){return this.elt[0].isEqualOnSide(t.elt[0],e)&&this.elt[1].isEqualOnSide(t.elt[1],e)},allPositionsEqual:function(t,e){return this.elt[t].allPositionsEqual(e)},toLine:function(t){this.elt[t].isArea()&&(this.elt[t]=new fn(this.elt[t].location[0]))},interfaces_:function(){return[]},getClass:function(){return gn}}),gn.toLineLabel=function(t){for(var e=new gn(L.NONE),n=0;2>n;n++)e.setLocation(n,t.getLocation(n));return e},e(dn.prototype,{computeRing:function(){if(null!==this.ring)return null;for(var t=new Array(this.pts.size()).fill(null),e=0;e<this.pts.size();e++)t[e]=this.pts.get(e);this.ring=this.geometryFactory.createLinearRing(t),this._isHole=he.isCCW(this.ring.getCoordinates())},isIsolated:function(){return 1===this.label.getGeometryCount()},computePoints:function(t){this.startDe=t;var e=t,n=!0;do{if(null===e)throw new sn("Found null DirectedEdge");if(e.getEdgeRing()===this)throw new sn("Directed Edge visited twice during ring-building at "+e.getCoordinate());this.edges.add(e);var i=e.getLabel();f.isTrue(i.isArea()),this.mergeLabel(i),this.addPoints(e.getEdge(),e.isForward(),n),n=!1,this.setEdgeRing(e,this),e=this.getNext(e)}while(e!==this.startDe)},getLinearRing:function(){return this.ring},getCoordinate:function(t){return this.pts.get(t)},computeMaxNodeDegree:function(){this.maxNodeDegree=0;var t=this.startDe;do{var e=t.getNode(),n=e.getEdges().getOutgoingDegree(this);n>this.maxNodeDegree&&(this.maxNodeDegree=n),t=this.getNext(t)}while(t!==this.startDe);this.maxNodeDegree*=2},addPoints:function(t,e,n){var i=t.getCoordinates();if(e){var r=1;n&&(r=0);for(var s=r;s<i.length;s++)this.pts.add(i[s])}else{var r=i.length-2;n&&(r=i.length-1);for(var s=r;s>=0;s--)this.pts.add(i[s])}},isHole:function(){return this._isHole},setInResult:function(){var t=this.startDe;do t.getEdge().setInResult(!0),t=t.getNext();while(t!==this.startDe)},containsPoint:function(t){var e=this.getLinearRing(),n=e.getEnvelopeInternal();if(!n.contains(t))return!1;if(!he.isPointInRing(t,e.getCoordinates()))return!1;for(var i=this.holes.iterator();i.hasNext();){var r=i.next();if(r.containsPoint(t))return!1}return!0},addHole:function(t){this.holes.add(t)},isShell:function(){return null===this.shell},getLabel:function(){return this.label},getEdges:function(){return this.edges},getMaxNodeDegree:function(){return this.maxNodeDegree<0&&this.computeMaxNodeDegree(),this.maxNodeDegree},getShell:function(){return this.shell},mergeLabel:function(){if(1===arguments.length){var t=arguments[0];this.mergeLabel(t,0),this.mergeLabel(t,1)}else if(2===arguments.length){var e=arguments[0],n=arguments[1],i=e.getLocation(n,cn.RIGHT);if(i===L.NONE)return null;if(this.label.getLocation(n)===L.NONE)return this.label.setLocation(n,i),null}},setShell:function(t){this.shell=t,null!==t&&t.addHole(this)},toPolygon:function(t){for(var e=new Array(this.holes.size()).fill(null),n=0;n<this.holes.size();n++)e[n]=this.holes.get(n).getLinearRing();var i=t.createPolygon(this.getLinearRing(),e);return i},interfaces_:function(){return[]},getClass:function(){return dn}}),h(pn,dn),e(pn.prototype,{setEdgeRing:function(t,e){t.setMinEdgeRing(e)},getNext:function(t){return t.getNextMin()},interfaces_:function(){return[]},getClass:function(){return pn}}),h(vn,dn),e(vn.prototype,{buildMinimalRings:function(){var t=new I,e=this.startDe;do{if(null===e.getMinEdgeRing()){var n=new pn(e,this.geometryFactory);t.add(n)}e=e.getNext()}while(e!==this.startDe);return t},setEdgeRing:function(t,e){t.setEdgeRing(e)},linkDirectedEdgesForMinimalEdgeRings:function(){var t=this.startDe;do{var e=t.getNode();e.getEdges().linkMinimalDirectedEdges(this),t=t.getNext()}while(t!==this.startDe)},getNext:function(t){return t.getNext()},interfaces_:function(){return[]},getClass:function(){return vn}}),e(mn.prototype,{setVisited:function(t){this._isVisited=t},setInResult:function(t){this._isInResult=t},isCovered:function(){return this._isCovered},isCoveredSet:function(){return this._isCoveredSet},setLabel:function(t){this.label=t},getLabel:function(){return this.label},setCovered:function(t){this._isCovered=t,this._isCoveredSet=!0},updateIM:function(t){f.isTrue(this.label.getGeometryCount()>=2,"found partial label"),this.computeIM(t)},isInResult:function(){return this._isInResult},isVisited:function(){return this._isVisited},interfaces_:function(){return[]},getClass:function(){return mn}}),h(yn,mn),e(yn.prototype,{isIncidentEdgeInResult:function(){for(var t=this.getEdges().getEdges().iterator();t.hasNext();){var e=t.next();if(e.getEdge().isInResult())return!0}return!1},isIsolated:function(){return 1===this.label.getGeometryCount()},getCoordinate:function(){return this.coord},print:function(t){t.println("node "+this.coord+" lbl: "+this.label)},computeIM:function(t){},computeMergedLocation:function(t,e){var n=L.NONE;if(n=this.label.getLocation(e),!t.isNull(e)){var i=t.getLocation(e);n!==L.BOUNDARY&&(n=i)}return n},setLabel:function(){if(2!==arguments.length)return mn.prototype.setLabel.apply(this,arguments);var t=arguments[0],e=arguments[1];null===this.label?this.label=new gn(t,e):this.label.setLocation(t,e)},getEdges:function(){return this.edges},mergeLabel:function(){if(arguments[0]instanceof yn){var t=arguments[0];this.mergeLabel(t.label)}else if(arguments[0]instanceof gn)for(var e=arguments[0],n=0;2>n;n++){var i=this.computeMergedLocation(e,n),r=this.label.getLocation(n);r===L.NONE&&this.label.setLocation(n,i)}},add:function(t){this.edges.insert(t),t.setNode(this)},setLabelBoundary:function(t){if(null===this.label)return null;var e=L.NONE;null!==this.label&&(e=this.label.getLocation(t));var n=null;switch(e){case L.BOUNDARY:n=L.INTERIOR;break;case L.INTERIOR:n=L.BOUNDARY;break;default:n=L.BOUNDARY}this.label.setLocation(t,n)},interfaces_:function(){return[]},getClass:function(){return yn}}),e(xn.prototype,{find:function(t){return this.nodeMap.get(t)},addNode:function(){if(arguments[0]instanceof g){var t=arguments[0],e=this.nodeMap.get(t);return null===e&&(e=this.nodeFact.createNode(t),this.nodeMap.put(t,e)),e}if(arguments[0]instanceof yn){var n=arguments[0],e=this.nodeMap.get(n.getCoordinate());return null===e?(this.nodeMap.put(n.getCoordinate(),n),n):(e.mergeLabel(n),e)}},print:function(t){for(var e=this.iterator();e.hasNext();){var n=e.next();n.print(t)}},iterator:function(){return this.nodeMap.values().iterator()},values:function(){return this.nodeMap.values()},getBoundaryNodes:function(t){for(var e=new I,n=this.iterator();n.hasNext();){var i=n.next();i.getLabel().getLocation(t)===L.BOUNDARY&&e.add(i)}return e},add:function(t){var e=t.getCoordinate(),n=this.addNode(e);n.add(t)},interfaces_:function(){return[]},getClass:function(){return xn}}),e(En.prototype,{compareDirection:function(t){return this.dx===t.dx&&this.dy===t.dy?0:this.quadrant>t.quadrant?1:this.quadrant<t.quadrant?-1:he.computeOrientation(t.p0,t.p1,this.p1)},getDy:function(){return this.dy},getCoordinate:function(){return this.p0},setNode:function(t){this.node=t},print:function(t){var e=Math.atan2(this.dy,this.dx),n=this.getClass().getName(),i=n.lastIndexOf("."),r=n.substring(i+1);t.print("  "+r+": "+this.p0+" - "+this.p1+" "+this.quadrant+":"+e+"   "+this.label)},compareTo:function(t){var e=t;return this.compareDirection(e)},getDirectedCoordinate:function(){return this.p1},getDx:function(){return this.dx},getLabel:function(){return this.label},getEdge:function(){return this.edge},getQuadrant:function(){return this.quadrant},getNode:function(){return this.node},toString:function(){var t=Math.atan2(this.dy,this.dx),e=this.getClass().getName(),n=e.lastIndexOf("."),i=e.substring(n+1);return"  "+i+": "+this.p0+" - "+this.p1+" "+this.quadrant+":"+t+"   "+this.label},computeLabel:function(t){},init:function(t,e){this.p0=t,this.p1=e,this.dx=e.x-t.x,this.dy=e.y-t.y,this.quadrant=Je.quadrant(this.dx,this.dy),f.isTrue(!(0===this.dx&&0===this.dy),"EdgeEnd with identical endpoints found")},interfaces_:function(){return[s]},getClass:function(){return En}}),h(In,En),e(In.prototype,{getNextMin:function(){return this.nextMin},getDepth:function(t){return this.depth[t]},setVisited:function(t){this._isVisited=t},computeDirectedLabel:function(){this.label=new gn(this.edge.getLabel()),this._isForward||this.label.flip()},getNext:function(){return this.next},setDepth:function(t,e){if(-999!==this.depth[t]&&this.depth[t]!==e)throw new sn("assigned depths do not match",this.getCoordinate());this.depth[t]=e},isInteriorAreaEdge:function t(){for(var t=!0,e=0;2>e;e++)this.label.isArea(e)&&this.label.getLocation(e,cn.LEFT)===L.INTERIOR&&this.label.getLocation(e,cn.RIGHT)===L.INTERIOR||(t=!1);return t},setNextMin:function(t){this.nextMin=t},print:function(t){En.prototype.print.call(this,t),t.print(" "+this.depth[cn.LEFT]+"/"+this.depth[cn.RIGHT]),t.print(" ("+this.getDepthDelta()+")"),this._isInResult&&t.print(" inResult")},setMinEdgeRing:function(t){this.minEdgeRing=t},isLineEdge:function(){var t=this.label.isLine(0)||this.label.isLine(1),e=!this.label.isArea(0)||this.label.allPositionsEqual(0,L.EXTERIOR),n=!this.label.isArea(1)||this.label.allPositionsEqual(1,L.EXTERIOR);return t&&e&&n},setEdgeRing:function(t){this.edgeRing=t},getMinEdgeRing:function(){return this.minEdgeRing},getDepthDelta:function(){var t=this.edge.getDepthDelta();return this._isForward||(t=-t),t},setInResult:function(t){this._isInResult=t},getSym:function(){return this.sym},isForward:function(){return this._isForward},getEdge:function(){return this.edge},printEdge:function(t){this.print(t),t.print(" "),this._isForward?this.edge.print(t):this.edge.printReverse(t)},setSym:function(t){this.sym=t},setVisitedEdge:function(t){this.setVisited(t),this.sym.setVisited(t)},setEdgeDepths:function(t,e){var n=this.getEdge().getDepthDelta();this._isForward||(n=-n);var i=1;t===cn.LEFT&&(i=-1);var r=cn.opposite(t),s=n*i,o=e+s;this.setDepth(t,e),this.setDepth(r,o)},getEdgeRing:function(){return this.edgeRing},isInResult:function(){return this._isInResult},setNext:function(t){this.next=t},isVisited:function(){return this._isVisited},interfaces_:function(){return[]},getClass:function(){return In}}),In.depthFactor=function(t,e){return t===L.EXTERIOR&&e===L.INTERIOR?1:t===L.INTERIOR&&e===L.EXTERIOR?-1:0},e(Nn.prototype,{createNode:function(t){return new yn(t,null)},interfaces_:function(){return[]},getClass:function(){return Nn}}),e(Cn.prototype,{printEdges:function(t){t.println("Edges:");for(var e=0;e<this.edges.size();e++){t.println("edge "+e+":");var n=this.edges.get(e);n.print(t),n.eiList.print(t)}},find:function(t){return this.nodes.find(t)},addNode:function(){if(arguments[0]instanceof yn){var t=arguments[0];return this.nodes.addNode(t)}if(arguments[0]instanceof g){var e=arguments[0];return this.nodes.addNode(e)}},getNodeIterator:function(){return this.nodes.iterator()},linkResultDirectedEdges:function(){for(var t=this.nodes.iterator();t.hasNext();){var e=t.next();e.getEdges().linkResultDirectedEdges()}},debugPrintln:function(t){A.out.println(t)},isBoundaryNode:function(t,e){var n=this.nodes.find(e);if(null===n)return!1;var i=n.getLabel();return null!==i&&i.getLocation(t)===L.BOUNDARY},linkAllDirectedEdges:function(){for(var t=this.nodes.iterator();t.hasNext();){var e=t.next();e.getEdges().linkAllDirectedEdges()}},matchInSameDirection:function(t,e,n,i){return t.equals(n)?he.computeOrientation(t,e,i)===he.COLLINEAR&&Je.quadrant(t,e)===Je.quadrant(n,i):!1},getEdgeEnds:function(){return this.edgeEndList},debugPrint:function(t){A.out.print(t)},getEdgeIterator:function(){return this.edges.iterator()},findEdgeInSameDirection:function(t,e){for(var n=0;n<this.edges.size();n++){var i=this.edges.get(n),r=i.getCoordinates();if(this.matchInSameDirection(t,e,r[0],r[1]))return i;if(this.matchInSameDirection(t,e,r[r.length-1],r[r.length-2]))return i}return null},insertEdge:function(t){this.edges.add(t)},findEdgeEnd:function(t){for(var e=this.getEdgeEnds().iterator();e.hasNext();){var n=e.next();if(n.getEdge()===t)return n}return null},addEdges:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.edges.add(n);var i=new In(n,!0),r=new In(n,!1);i.setSym(r),r.setSym(i),this.add(i),this.add(r)}},add:function(t){this.nodes.add(t),this.edgeEndList.add(t)},getNodes:function(){return this.nodes.values()},findEdge:function(t,e){for(var n=0;n<this.edges.size();n++){var i=this.edges.get(n),r=i.getCoordinates();if(t.equals(r[0])&&e.equals(r[1]))return i}return null},interfaces_:function(){return[]},getClass:function(){return Cn}}),Cn.linkResultDirectedEdges=function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();n.getEdges().linkResultDirectedEdges()}},e(Sn.prototype,{sortShellsAndHoles:function(t,e,n){for(var i=t.iterator();i.hasNext();){var r=i.next();r.isHole()?n.add(r):e.add(r)}},computePolygons:function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next(),r=i.toPolygon(this.geometryFactory);e.add(r)}return e},placeFreeHoles:function(t,e){for(var n=e.iterator();n.hasNext();){var i=n.next();if(null===i.getShell()){var r=this.findEdgeRingContaining(i,t);if(null===r)throw new sn("unable to assign hole to a shell",i.getCoordinate(0));i.setShell(r)}}},buildMinimalEdgeRings:function(t,e,n){for(var i=new I,r=t.iterator();r.hasNext();){var s=r.next();if(s.getMaxNodeDegree()>2){s.linkDirectedEdgesForMinimalEdgeRings();var o=s.buildMinimalRings(),a=this.findShell(o);null!==a?(this.placePolygonHoles(a,o),e.add(a)):n.addAll(o)}else i.add(s)}return i},containsPoint:function(t){for(var e=this.shellList.iterator();e.hasNext();){var n=e.next();if(n.containsPoint(t))return!0}return!1},buildMaximalEdgeRings:function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next();if(i.isInResult()&&i.getLabel().isArea()&&null===i.getEdgeRing()){var r=new vn(i,this.geometryFactory);e.add(r),r.setInResult()}}return e},placePolygonHoles:function(t,e){for(var n=e.iterator();n.hasNext();){var i=n.next();i.isHole()&&i.setShell(t)}},getPolygons:function(){var t=this.computePolygons(this.shellList);return t},findEdgeRingContaining:function(t,e){for(var n=t.getLinearRing(),i=n.getEnvelopeInternal(),r=n.getCoordinateN(0),s=null,o=null,a=e.iterator();a.hasNext();){var u=a.next(),l=u.getLinearRing(),h=l.getEnvelopeInternal();null!==s&&(o=s.getLinearRing().getEnvelopeInternal());var c=!1;h.contains(i)&&he.isPointInRing(r,l.getCoordinates())&&(c=!0),c&&(null===s||o.contains(h))&&(s=u)}return s},findShell:function(t){for(var e=0,n=null,i=t.iterator();i.hasNext();){var r=i.next();r.isHole()||(n=r,e++)}return f.isTrue(1>=e,"found two shells in MinimalEdgeRing list"),n},add:function(){if(1===arguments.length){var t=arguments[0];this.add(t.getEdgeEnds(),t.getNodes())}else if(2===arguments.length){var e=arguments[0],n=arguments[1];Cn.linkResultDirectedEdges(n);var i=this.buildMaximalEdgeRings(e),r=new I,s=this.buildMinimalEdgeRings(i,this.shellList,r);this.sortShellsAndHoles(s,this.shellList,r),this.placeFreeHoles(this.shellList,r)}},interfaces_:function(){return[]},getClass:function(){return Sn}}),e(wn.prototype,{collectLines:function(t){for(var e=this.op.getGraph().getEdgeEnds().iterator();e.hasNext();){var n=e.next();this.collectLineEdge(n,t,this.lineEdgesList),this.collectBoundaryTouchEdge(n,t,this.lineEdgesList)}},labelIsolatedLine:function(t,e){var n=this.ptLocator.locate(t.getCoordinate(),this.op.getArgGeometry(e));t.getLabel().setLocation(e,n)},build:function(t){return this.findCoveredLineEdges(),this.collectLines(t),this.buildLines(t),this.resultLineList},collectLineEdge:function(t,e,n){var i=t.getLabel(),r=t.getEdge();t.isLineEdge()&&(t.isVisited()||!ii.isResultOfOp(i,e)||r.isCovered()||(n.add(r),t.setVisitedEdge(!0)))},findCoveredLineEdges:function(){for(var t=this.op.getGraph().getNodes().iterator();t.hasNext();){var e=t.next();e.getEdges().findCoveredLineEdges()}for(var n=this.op.getGraph().getEdgeEnds().iterator();n.hasNext();){var i=n.next(),r=i.getEdge();if(i.isLineEdge()&&!r.isCoveredSet()){var s=this.op.isCoveredByA(i.getCoordinate());r.setCovered(s)}}},labelIsolatedLines:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next(),i=n.getLabel();n.isIsolated()&&(i.isNull(0)?this.labelIsolatedLine(n,0):this.labelIsolatedLine(n,1))}},buildLines:function(t){for(var e=this.lineEdgesList.iterator();e.hasNext();){var n=e.next(),i=(n.getLabel(),this.geometryFactory.createLineString(n.getCoordinates()));this.resultLineList.add(i),n.setInResult(!0)}},collectBoundaryTouchEdge:function(t,e,n){var i=t.getLabel();return t.isLineEdge()?null:t.isVisited()?null:t.isInteriorAreaEdge()?null:t.getEdge().isInResult()?null:(f.isTrue(!(t.isInResult()||t.getSym().isInResult())||!t.getEdge().isInResult()),void(ii.isResultOfOp(i,e)&&e===ii.INTERSECTION&&(n.add(t.getEdge()),t.setVisitedEdge(!0))))},interfaces_:function(){return[]},getClass:function(){return wn}}),e(Ln.prototype,{filterCoveredNodeToPoint:function(t){var e=t.getCoordinate();if(!this.op.isCoveredByLA(e)){var n=this.geometryFactory.createPoint(e);this.resultPointList.add(n)}},extractNonCoveredResultNodes:function(t){for(var e=this.op.getGraph().getNodes().iterator();e.hasNext();){var n=e.next();if(!(n.isInResult()||n.isIncidentEdgeInResult()||0!==n.getEdges().getDegree()&&t!==ii.INTERSECTION)){var i=n.getLabel();ii.isResultOfOp(i,t)&&this.filterCoveredNodeToPoint(n)}}},build:function(t){return this.extractNonCoveredResultNodes(t),this.resultPointList},interfaces_:function(){return[]},getClass:function(){return Ln}}),e(Rn.prototype,{locate:function(t){},interfaces_:function(){return[]},getClass:function(){return Rn}}),e(Tn.prototype,{locate:function(t){return Tn.locate(t,this.geom)},interfaces_:function(){return[Rn]},getClass:function(){return Tn}}),Tn.isPointInRing=function(t,e){return e.getEnvelopeInternal().intersects(t)?he.isPointInRing(t,e.getCoordinates()):!1},Tn.containsPointInPolygon=function(t,e){if(e.isEmpty())return!1;var n=e.getExteriorRing();if(!Tn.isPointInRing(t,n))return!1;for(var i=0;i<e.getNumInteriorRing();i++){var r=e.getInteriorRingN(i);if(Tn.isPointInRing(t,r))return!1}return!0},Tn.containsPoint=function(t,e){if(e instanceof Tt)return Tn.containsPointInPolygon(t,e);if(e instanceof ft)for(var n=new Re(e);n.hasNext();){var i=n.next();if(i!==e&&Tn.containsPoint(t,i))return!0}return!1},Tn.locate=function(t,e){return e.isEmpty()?L.EXTERIOR:Tn.containsPoint(t,e)?L.INTERIOR:L.EXTERIOR},e(Pn.prototype,{getNextCW:function(t){this.getEdges();var e=this.edgeList.indexOf(t),n=e-1;return 0===e&&(n=this.edgeList.size()-1),this.edgeList.get(n)},propagateSideLabels:function(t){for(var e=L.NONE,n=this.iterator();n.hasNext();){var i=n.next(),r=i.getLabel();r.isArea(t)&&r.getLocation(t,cn.LEFT)!==L.NONE&&(e=r.getLocation(t,cn.LEFT))}if(e===L.NONE)return null;for(var s=e,n=this.iterator();n.hasNext();){var i=n.next(),r=i.getLabel();if(r.getLocation(t,cn.ON)===L.NONE&&r.setLocation(t,cn.ON,s),r.isArea(t)){var o=r.getLocation(t,cn.LEFT),a=r.getLocation(t,cn.RIGHT);if(a!==L.NONE){if(a!==s)throw new sn("side location conflict",i.getCoordinate());o===L.NONE&&f.shouldNeverReachHere("found single null side (at "+i.getCoordinate()+")"),s=o}else f.isTrue(r.getLocation(t,cn.LEFT)===L.NONE,"found single null side"),r.setLocation(t,cn.RIGHT,s),r.setLocation(t,cn.LEFT,s)}}},getCoordinate:function(){var t=this.iterator();if(!t.hasNext())return null;var e=t.next();return e.getCoordinate()},print:function(t){A.out.println("EdgeEndStar:   "+this.getCoordinate());for(var e=this.iterator();e.hasNext();){var n=e.next();n.print(t)}},isAreaLabelsConsistent:function(t){return this.computeEdgeEndLabels(t.getBoundaryNodeRule()),this.checkAreaLabelsConsistent(0)},checkAreaLabelsConsistent:function(t){var e=this.getEdges();if(e.size()<=0)return!0;var n=e.size()-1,i=e.get(n).getLabel(),r=i.getLocation(t,cn.LEFT);f.isTrue(r!==L.NONE,"Found unlabelled area edge");for(var s=r,o=this.iterator();o.hasNext();){var a=o.next(),u=a.getLabel();
f.isTrue(u.isArea(t),"Found non-area edge");var l=u.getLocation(t,cn.LEFT),h=u.getLocation(t,cn.RIGHT);if(l===h)return!1;if(h!==s)return!1;s=l}return!0},findIndex:function(t){this.iterator();for(var e=0;e<this.edgeList.size();e++){var n=this.edgeList.get(e);if(n===t)return e}return-1},iterator:function(){return this.getEdges().iterator()},getEdges:function(){return null===this.edgeList&&(this.edgeList=new I(this.edgeMap.values())),this.edgeList},getLocation:function(t,e,n){return this.ptInAreaLocation[t]===L.NONE&&(this.ptInAreaLocation[t]=Tn.locate(e,n[t].getGeometry())),this.ptInAreaLocation[t]},toString:function(){var t=new P;t.append("EdgeEndStar:   "+this.getCoordinate()),t.append("\n");for(var e=this.iterator();e.hasNext();){var n=e.next();t.append(n),t.append("\n")}return t.toString()},computeEdgeEndLabels:function(t){for(var e=this.iterator();e.hasNext();){var n=e.next();n.computeLabel(t)}},computeLabelling:function(t){this.computeEdgeEndLabels(t[0].getBoundaryNodeRule()),this.propagateSideLabels(0),this.propagateSideLabels(1);for(var e=[!1,!1],n=this.iterator();n.hasNext();)for(var i=n.next(),r=i.getLabel(),s=0;2>s;s++)r.isLine(s)&&r.getLocation(s)===L.BOUNDARY&&(e[s]=!0);for(var n=this.iterator();n.hasNext();)for(var i=n.next(),r=i.getLabel(),s=0;2>s;s++)if(r.isAnyNull(s)){var o=L.NONE;if(e[s])o=L.EXTERIOR;else{var a=i.getCoordinate();o=this.getLocation(s,a,t)}r.setAllLocationsIfNull(s,o)}},getDegree:function(){return this.edgeMap.size()},insertEdgeEnd:function(t,e){this.edgeMap.put(t,e),this.edgeList=null},interfaces_:function(){return[]},getClass:function(){return Pn}}),h(bn,Pn),e(bn.prototype,{linkResultDirectedEdges:function(){this.getResultAreaEdges();for(var t=null,e=null,n=this.SCANNING_FOR_INCOMING,i=0;i<this.resultAreaEdgeList.size();i++){var r=this.resultAreaEdgeList.get(i),s=r.getSym();if(r.getLabel().isArea())switch(null===t&&r.isInResult()&&(t=r),n){case this.SCANNING_FOR_INCOMING:if(!s.isInResult())continue;e=s,n=this.LINKING_TO_OUTGOING;break;case this.LINKING_TO_OUTGOING:if(!r.isInResult())continue;e.setNext(r),n=this.SCANNING_FOR_INCOMING}}if(n===this.LINKING_TO_OUTGOING){if(null===t)throw new sn("no outgoing dirEdge found",this.getCoordinate());f.isTrue(t.isInResult(),"unable to link last incoming dirEdge"),e.setNext(t)}},insert:function(t){var e=t;this.insertEdgeEnd(e,e)},getRightmostEdge:function(){var t=this.getEdges(),e=t.size();if(1>e)return null;var n=t.get(0);if(1===e)return n;var i=t.get(e-1),r=n.getQuadrant(),s=i.getQuadrant();if(Je.isNorthern(r)&&Je.isNorthern(s))return n;if(!Je.isNorthern(r)&&!Je.isNorthern(s))return i;return 0!==n.getDy()?n:0!==i.getDy()?i:(f.shouldNeverReachHere("found two horizontal edges incident on node"),null)},print:function(t){A.out.println("DirectedEdgeStar: "+this.getCoordinate());for(var e=this.iterator();e.hasNext();){var n=e.next();t.print("out "),n.print(t),t.println(),t.print("in "),n.getSym().print(t),t.println()}},getResultAreaEdges:function(){if(null!==this.resultAreaEdgeList)return this.resultAreaEdgeList;this.resultAreaEdgeList=new I;for(var t=this.iterator();t.hasNext();){var e=t.next();(e.isInResult()||e.getSym().isInResult())&&this.resultAreaEdgeList.add(e)}return this.resultAreaEdgeList},updateLabelling:function(t){for(var e=this.iterator();e.hasNext();){var n=e.next(),i=n.getLabel();i.setAllLocationsIfNull(0,t.getLocation(0)),i.setAllLocationsIfNull(1,t.getLocation(1))}},linkAllDirectedEdges:function(){this.getEdges();for(var t=null,e=null,n=this.edgeList.size()-1;n>=0;n--){var i=this.edgeList.get(n),r=i.getSym();null===e&&(e=r),null!==t&&r.setNext(t),t=i}e.setNext(t)},computeDepths:function(){if(1===arguments.length){var t=arguments[0],e=this.findIndex(t),n=(t.getLabel(),t.getDepth(cn.LEFT)),i=t.getDepth(cn.RIGHT),r=this.computeDepths(e+1,this.edgeList.size(),n),s=this.computeDepths(0,e,r);if(s!==i)throw new sn("depth mismatch at "+t.getCoordinate())}else if(3===arguments.length){for(var o=arguments[0],a=arguments[1],u=arguments[2],l=u,h=o;a>h;h++){var c=this.edgeList.get(h);c.getLabel();c.setEdgeDepths(cn.RIGHT,l),l=c.getDepth(cn.LEFT)}return l}},mergeSymLabels:function(){for(var t=this.iterator();t.hasNext();){var e=t.next(),n=e.getLabel();n.merge(e.getSym().getLabel())}},linkMinimalDirectedEdges:function(t){for(var e=null,n=null,i=this.SCANNING_FOR_INCOMING,r=this.resultAreaEdgeList.size()-1;r>=0;r--){var s=this.resultAreaEdgeList.get(r),o=s.getSym();switch(null===e&&s.getEdgeRing()===t&&(e=s),i){case this.SCANNING_FOR_INCOMING:if(o.getEdgeRing()!==t)continue;n=o,i=this.LINKING_TO_OUTGOING;break;case this.LINKING_TO_OUTGOING:if(s.getEdgeRing()!==t)continue;n.setNextMin(s),i=this.SCANNING_FOR_INCOMING}}i===this.LINKING_TO_OUTGOING&&(f.isTrue(null!==e,"found null for first outgoing dirEdge"),f.isTrue(e.getEdgeRing()===t,"unable to link last incoming dirEdge"),n.setNextMin(e))},getOutgoingDegree:function(){if(0===arguments.length){for(var t=0,e=this.iterator();e.hasNext();){var n=e.next();n.isInResult()&&t++}return t}if(1===arguments.length){for(var i=arguments[0],t=0,e=this.iterator();e.hasNext();){var n=e.next();n.getEdgeRing()===i&&t++}return t}},getLabel:function(){return this.label},findCoveredLineEdges:function(){for(var t=L.NONE,e=this.iterator();e.hasNext();){var n=e.next(),i=n.getSym();if(!n.isLineEdge()){if(n.isInResult()){t=L.INTERIOR;break}if(i.isInResult()){t=L.EXTERIOR;break}}}if(t===L.NONE)return null;for(var r=t,e=this.iterator();e.hasNext();){var n=e.next(),i=n.getSym();n.isLineEdge()?n.getEdge().setCovered(r===L.INTERIOR):(n.isInResult()&&(r=L.EXTERIOR),i.isInResult()&&(r=L.INTERIOR))}},computeLabelling:function(t){Pn.prototype.computeLabelling.call(this,t),this.label=new gn(L.NONE);for(var e=this.iterator();e.hasNext();)for(var n=e.next(),i=n.getEdge(),r=i.getLabel(),s=0;2>s;s++){var o=r.getLocation(s);o!==L.INTERIOR&&o!==L.BOUNDARY||this.label.setLocation(s,L.INTERIOR)}},interfaces_:function(){return[]},getClass:function(){return bn}}),h(On,Nn),e(On.prototype,{createNode:function(t){return new yn(t,new bn)},interfaces_:function(){return[]},getClass:function(){return On}}),e(_n.prototype,{computeIntersections:function(t,e){this.mce.computeIntersectsForChain(this.chainIndex,t.mce,t.chainIndex,e)},interfaces_:function(){return[]},getClass:function(){return _n}}),e(Mn.prototype,{isDelete:function(){return this.eventType===Mn.DELETE},setDeleteEventIndex:function(t){this.deleteEventIndex=t},getObject:function(){return this.obj},compareTo:function(t){var e=t;return this.xValue<e.xValue?-1:this.xValue>e.xValue?1:this.eventType<e.eventType?-1:this.eventType>e.eventType?1:0},getInsertEvent:function(){return this.insertEvent},isInsert:function(){return this.eventType===Mn.INSERT},isSameLabel:function(t){return null===this.label?!1:this.label===t.label},getDeleteEventIndex:function(){return this.deleteEventIndex},interfaces_:function(){return[s]},getClass:function(){return Mn}}),Mn.INSERT=1,Mn.DELETE=2,e(Dn.prototype,{interfaces_:function(){return[]},getClass:function(){return Dn}}),e(An.prototype,{isTrivialIntersection:function(t,e,n,i){if(t===n&&1===this.li.getIntersectionNum()){if(An.isAdjacentSegments(e,i))return!0;if(t.isClosed()){var r=t.getNumPoints()-1;if(0===e&&i===r||0===i&&e===r)return!0}}return!1},getProperIntersectionPoint:function(){return this.properIntersectionPoint},setIsDoneIfProperInt:function(t){this.isDoneWhenProperInt=t},hasProperInteriorIntersection:function(){return this.hasProperInterior},isBoundaryPointInternal:function(t,e){for(var n=e.iterator();n.hasNext();){var i=n.next(),r=i.getCoordinate();if(t.isIntersection(r))return!0}return!1},hasProperIntersection:function(){return this.hasProper},hasIntersection:function(){return this._hasIntersection},isDone:function(){return this._isDone},isBoundaryPoint:function(t,e){return null===e?!1:this.isBoundaryPointInternal(t,e[0])?!0:!!this.isBoundaryPointInternal(t,e[1])},setBoundaryNodes:function(t,e){this.bdyNodes=new Array(2).fill(null),this.bdyNodes[0]=t,this.bdyNodes[1]=e},addIntersections:function(t,e,n,i){if(t===n&&e===i)return null;this.numTests++;var r=t.getCoordinates()[e],s=t.getCoordinates()[e+1],o=n.getCoordinates()[i],a=n.getCoordinates()[i+1];this.li.computeIntersection(r,s,o,a),this.li.hasIntersection()&&(this.recordIsolated&&(t.setIsolated(!1),n.setIsolated(!1)),this.numIntersections++,this.isTrivialIntersection(t,e,n,i)||(this._hasIntersection=!0,!this.includeProper&&this.li.isProper()||(t.addIntersections(this.li,e,0),n.addIntersections(this.li,i,1)),this.li.isProper()&&(this.properIntersectionPoint=this.li.getIntersection(0).copy(),this.hasProper=!0,this.isDoneWhenProperInt&&(this._isDone=!0),this.isBoundaryPoint(this.li,this.bdyNodes)||(this.hasProperInterior=!0))))},interfaces_:function(){return[]},getClass:function(){return An}}),An.isAdjacentSegments=function(t,e){return 1===Math.abs(t-e)},h(Fn,Dn),e(Fn.prototype,{prepareEvents:function(){ho.sort(this.events);for(var t=0;t<this.events.size();t++){var e=this.events.get(t);e.isDelete()&&e.getInsertEvent().setDeleteEventIndex(t)}},computeIntersections:function(){if(1===arguments.length){var t=arguments[0];this.nOverlaps=0,this.prepareEvents();for(var e=0;e<this.events.size();e++){var n=this.events.get(e);if(n.isInsert()&&this.processOverlaps(e,n.getDeleteEventIndex(),n,t),t.isDone())break}}else if(3===arguments.length)if(arguments[2]instanceof An&&R(arguments[0],y)&&R(arguments[1],y)){var i=arguments[0],r=arguments[1],s=arguments[2];this.addEdges(i,i),this.addEdges(r,r),this.computeIntersections(s)}else if("boolean"==typeof arguments[2]&&R(arguments[0],y)&&arguments[1]instanceof An){var o=arguments[0],a=arguments[1],u=arguments[2];u?this.addEdges(o,null):this.addEdges(o),this.computeIntersections(a)}},addEdge:function(t,e){for(var n=t.getMonotoneChainEdge(),i=n.getStartIndexes(),r=0;r<i.length-1;r++){var s=new _n(n,r),o=new Mn(e,n.getMinX(r),s);this.events.add(o),this.events.add(new Mn(n.getMaxX(r),o))}},processOverlaps:function(t,e,n,i){for(var r=n.getObject(),s=t;e>s;s++){var o=this.events.get(s);if(o.isInsert()){var a=o.getObject();n.isSameLabel(o)||(r.computeIntersections(a,i),this.nOverlaps++)}}},addEdges:function(){if(1===arguments.length)for(var t=arguments[0],e=t.iterator();e.hasNext();){var n=e.next();this.addEdge(n,n)}else if(2===arguments.length)for(var i=arguments[0],r=arguments[1],e=i.iterator();e.hasNext();){var n=e.next();this.addEdge(n,r)}},interfaces_:function(){return[]},getClass:function(){return Fn}}),e(Gn.prototype,{getMin:function(){return this.min},intersects:function(t,e){return!(this.min>e||this.max<t)},getMax:function(){return this.max},toString:function(){return se.toLineString(new g(this.min,0),new g(this.max,0))},interfaces_:function(){return[]},getClass:function(){return Gn}}),e(qn.prototype,{compare:function(t,e){var n=t,i=e,r=(n.min+n.max)/2,s=(i.min+i.max)/2;return s>r?-1:r>s?1:0},interfaces_:function(){return[a]},getClass:function(){return qn}}),Gn.NodeComparator=qn,h(Bn,Gn),e(Bn.prototype,{query:function(t,e,n){return this.intersects(t,e)?void n.visitItem(this.item):null},interfaces_:function(){return[]},getClass:function(){return Bn}}),h(zn,Gn),e(zn.prototype,{buildExtent:function(t,e){this.min=Math.min(t.min,e.min),this.max=Math.max(t.max,e.max)},query:function(t,e,n){return this.intersects(t,e)?(null!==this.node1&&this.node1.query(t,e,n),void(null!==this.node2&&this.node2.query(t,e,n))):null},interfaces_:function(){return[]},getClass:function(){return zn}}),e(Vn.prototype,{buildTree:function(){ho.sort(this.leaves,new IntervalRTreeNode.NodeComparator);for(var t=this.leaves,e=null,n=new I;;){if(this.buildLevel(t,n),1===n.size())return n.get(0);e=t,t=n,n=e}},insert:function(t,e,n){if(null!==this.root)throw new IllegalStateException("Index cannot be added to once it has been queried");this.leaves.add(new Bn(t,e,n))},query:function(t,e,n){this.init(),this.root.query(t,e,n)},buildRoot:function(){return null!==this.root?null:void(this.root=this.buildTree())},printNode:function(t){A.out.println(se.toLineString(new g(t.min,this.level),new g(t.max,this.level)))},init:function(){return null!==this.root?null:void this.buildRoot()},buildLevel:function(t,e){this.level++,e.clear();for(var n=0;n<t.size();n+=2){var i=t.get(n),r=n+1<t.size()?t.get(n):null;if(null===r)e.add(i);else{var s=new zn(t.get(n),t.get(n+1));e.add(s)}}},interfaces_:function(){return[]},getClass:function(){return Vn}}),e(kn.prototype,{filter:function(t){if(this.isForcedToLineString&&t instanceof bt){var e=t.getFactory().createLineString(t.getCoordinateSequence());return this.lines.add(e),null}t instanceof St&&this.lines.add(t)},setForceToLineString:function(t){this.isForcedToLineString=t},interfaces_:function(){return[q]},getClass:function(){return kn}}),kn.getGeometry=function(){if(1===arguments.length){var t=arguments[0];return t.getFactory().buildGeometry(kn.getLines(t))}if(2===arguments.length){var e=arguments[0],n=arguments[1];return e.getFactory().buildGeometry(kn.getLines(e,n))}},kn.getLines=function(){if(1===arguments.length){var t=arguments[0];return kn.getLines(t,!1)}if(2===arguments.length){if(R(arguments[0],v)&&R(arguments[1],v)){for(var e=arguments[0],n=arguments[1],i=e.iterator();i.hasNext();){var r=i.next();kn.getLines(r,n)}return n}if(arguments[0]instanceof B&&"boolean"==typeof arguments[1]){var s=arguments[0],o=arguments[1],a=new I;return s.apply(new kn(a,o)),a}if(arguments[0]instanceof B&&R(arguments[1],v)){var u=arguments[0],l=arguments[1];return u instanceof St?l.add(u):u.apply(new kn(l)),l}}else if(3===arguments.length){if("boolean"==typeof arguments[2]&&R(arguments[0],v)&&R(arguments[1],v)){for(var h=arguments[0],c=arguments[1],f=arguments[2],i=h.iterator();i.hasNext();){var r=i.next();kn.getLines(r,c,f)}return c}if("boolean"==typeof arguments[2]&&arguments[0]instanceof B&&R(arguments[1],v)){var g=arguments[0],d=arguments[1],p=arguments[2];return g.apply(new kn(d,p)),d}}},e(Yn.prototype,{visitItem:function(t){this.items.add(t)},getItems:function(){return this.items},interfaces_:function(){return[Ae]},getClass:function(){return Yn}}),e(Un.prototype,{locate:function(t){var e=new le(t),n=new Xn(e);return this.index.query(t.y,t.y,n),e.getLocation()},interfaces_:function(){return[Rn]},getClass:function(){return Un}}),e(Xn.prototype,{visitItem:function(t){var e=t;this.counter.countSegment(e.getCoordinate(0),e.getCoordinate(1))},interfaces_:function(){return[Ae]},getClass:function(){return Xn}}),e(Hn.prototype,{init:function(t){for(var e=kn.getLines(t),n=e.iterator();n.hasNext();){var i=n.next(),r=i.getCoordinates();this.addLine(r)}},addLine:function(t){for(var e=1;e<t.length;e++){var n=new ce(t[e-1],t[e]),i=Math.min(n.p0.y,n.p1.y),r=Math.max(n.p0.y,n.p1.y);this.index.insert(i,r,n)}},query:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=new Yn;return this.index.query(t,e,n),n.getItems()}if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];this.index.query(i,r,s)}},interfaces_:function(){return[]},getClass:function(){return Hn}}),Un.SegmentVisitor=Xn,Un.IntervalIndexedGeometry=Hn,e(Wn.prototype,{getSegmentIndex:function(){return this.segmentIndex},getCoordinate:function(){return this.coord},print:function(t){t.print(this.coord),t.print(" seg # = "+this.segmentIndex),t.println(" dist = "+this.dist)},compareTo:function(t){var e=t;return this.compare(e.segmentIndex,e.dist)},isEndPoint:function(t){return 0===this.segmentIndex&&0===this.dist?!0:this.segmentIndex===t},toString:function(){return this.coord+" seg # = "+this.segmentIndex+" dist = "+this.dist},getDistance:function(){return this.dist},compare:function(t,e){return this.segmentIndex<t?-1:this.segmentIndex>t?1:this.dist<e?-1:this.dist>e?1:0},interfaces_:function(){return[s]},getClass:function(){return Wn}}),e(jn.prototype,{print:function(t){t.println("Intersections:");for(var e=this.iterator();e.hasNext();){var n=e.next();n.print(t)}},iterator:function(){return this.nodeMap.values().iterator()},addSplitEdges:function(t){this.addEndpoints();for(var e=this.iterator(),n=e.next();e.hasNext();){var i=e.next(),r=this.createSplitEdge(n,i);t.add(r),n=i}},addEndpoints:function(){var t=this.edge.pts.length-1;this.add(this.edge.pts[0],0,0),this.add(this.edge.pts[t],t,0)},createSplitEdge:function(t,e){var n=e.segmentIndex-t.segmentIndex+2,i=this.edge.pts[e.segmentIndex],r=e.dist>0||!e.coord.equals2D(i);r||n--;var s=new Array(n).fill(null),o=0;s[o++]=new g(t.coord);for(var a=t.segmentIndex+1;a<=e.segmentIndex;a++)s[o++]=this.edge.pts[a];return r&&(s[o]=e.coord),new Jn(s,new gn(this.edge.label))},add:function(t,e,n){var i=new Wn(t,e,n),r=this.nodeMap.get(i);return null!==r?r:(this.nodeMap.put(i,i),i)},isIntersection:function(t){for(var e=this.iterator();e.hasNext();){var n=e.next();if(n.coord.equals(t))return!0}return!1},interfaces_:function(){return[]},getClass:function(){return jn}}),e(Kn.prototype,{getChainStartIndices:function(t){var e=0,n=new I;n.add(new b(e));do{var i=this.findChainEnd(t,e);n.add(new b(i)),e=i}while(e<t.length-1);var r=Kn.toIntArray(n);return r},findChainEnd:function(t,e){for(var n=Je.quadrant(t[e],t[e+1]),i=e+1;i<t.length;){var r=Je.quadrant(t[i-1],t[i]);if(r!==n)break;i++}return i-1},interfaces_:function(){return[]},getClass:function(){return Kn}}),Kn.toIntArray=function(t){for(var e=new Array(t.size()).fill(null),n=0;n<e.length;n++)e[n]=t.get(n).intValue();return e},e(Zn.prototype,{getCoordinates:function(){return this.pts},getMaxX:function(t){var e=this.pts[this.startIndex[t]].x,n=this.pts[this.startIndex[t+1]].x;return e>n?e:n},getMinX:function(t){var e=this.pts[this.startIndex[t]].x,n=this.pts[this.startIndex[t+1]].x;return n>e?e:n},computeIntersectsForChain:function(){if(4===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];this.computeIntersectsForChain(this.startIndex[t],this.startIndex[t+1],e,e.startIndex[n],e.startIndex[n+1],i)}else if(6===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],a=arguments[3],u=arguments[4],l=arguments[5],h=this.pts[r],c=this.pts[s],f=o.pts[a],g=o.pts[u];if(s-r===1&&u-a===1)return l.addIntersections(this.e,r,o.e,a),null;if(this.env1.init(h,c),this.env2.init(f,g),!this.env1.intersects(this.env2))return null;var d=Math.trunc((r+s)/2),p=Math.trunc((a+u)/2);d>r&&(p>a&&this.computeIntersectsForChain(r,d,o,a,p,l),u>p&&this.computeIntersectsForChain(r,d,o,p,u,l)),s>d&&(p>a&&this.computeIntersectsForChain(d,s,o,a,p,l),u>p&&this.computeIntersectsForChain(d,s,o,p,u,l))}},getStartIndexes:function(){return this.startIndex},computeIntersects:function(t,e){for(var n=0;n<this.startIndex.length-1;n++)for(var i=0;i<t.startIndex.length-1;i++)this.computeIntersectsForChain(n,t,i,e)},interfaces_:function(){return[]},getClass:function(){return Zn}}),e(Qn.prototype,{getDepth:function(t,e){return this.depth[t][e]},setDepth:function(t,e,n){this.depth[t][e]=n},isNull:function(){if(0===arguments.length){for(var t=0;2>t;t++)for(var e=0;3>e;e++)if(this.depth[t][e]!==Qn.NULL_VALUE)return!1;return!0}if(1===arguments.length){var n=arguments[0];return this.depth[n][1]===Qn.NULL_VALUE}if(2===arguments.length){var i=arguments[0],r=arguments[1];return this.depth[i][r]===Qn.NULL_VALUE}},normalize:function(){for(var t=0;2>t;t++)if(!this.isNull(t)){var e=this.depth[t][1];this.depth[t][2]<e&&(e=this.depth[t][2]),0>e&&(e=0);for(var n=1;3>n;n++){var i=0;this.depth[t][n]>e&&(i=1),this.depth[t][n]=i}}},getDelta:function(t){return this.depth[t][cn.RIGHT]-this.depth[t][cn.LEFT]},getLocation:function(t,e){return this.depth[t][e]<=0?L.EXTERIOR:L.INTERIOR},toString:function(){return"A: "+this.depth[0][1]+","+this.depth[0][2]+" B: "+this.depth[1][1]+","+this.depth[1][2]},add:function(){if(1===arguments.length)for(var t=arguments[0],e=0;2>e;e++)for(var n=1;3>n;n++){var i=t.getLocation(e,n);i!==L.EXTERIOR&&i!==L.INTERIOR||(this.isNull(e,n)?this.depth[e][n]=Qn.depthAtLocation(i):this.depth[e][n]+=Qn.depthAtLocation(i))}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];o===L.INTERIOR&&this.depth[r][s]++}},interfaces_:function(){return[]},getClass:function(){return Qn}}),Qn.depthAtLocation=function(t){return t===L.EXTERIOR?0:t===L.INTERIOR?1:Qn.NULL_VALUE},Qn.NULL_VALUE=-1,h(Jn,mn),e(Jn.prototype,{getDepth:function(){return this.depth},getCollapsedEdge:function(){var t=new Array(2).fill(null);t[0]=this.pts[0],t[1]=this.pts[1];var e=new Jn(t,gn.toLineLabel(this.label));return e},isIsolated:function(){return this._isIsolated},getCoordinates:function(){return this.pts},setIsolated:function(t){this._isIsolated=t},setName:function(t){this.name=t},equals:function(t){if(!(t instanceof Jn))return!1;var e=t;if(this.pts.length!==e.pts.length)return!1;for(var n=!0,i=!0,r=this.pts.length,s=0;s<this.pts.length;s++)if(this.pts[s].equals2D(e.pts[s])||(n=!1),this.pts[s].equals2D(e.pts[--r])||(i=!1),!n&&!i)return!1;return!0},getCoordinate:function(){if(0===arguments.length)return this.pts.length>0?this.pts[0]:null;if(1===arguments.length){var t=arguments[0];return this.pts[t]}},print:function(t){t.print("edge "+this.name+": "),t.print("LINESTRING (");for(var e=0;e<this.pts.length;e++)e>0&&t.print(","),t.print(this.pts[e].x+" "+this.pts[e].y);t.print(")  "+this.label+" "+this.depthDelta)},computeIM:function(t){Jn.updateIM(this.label,t)},isCollapsed:function(){return this.label.isArea()?3!==this.pts.length?!1:!!this.pts[0].equals(this.pts[2]):!1},isClosed:function(){return this.pts[0].equals(this.pts[this.pts.length-1])},getMaximumSegmentIndex:function(){return this.pts.length-1},getDepthDelta:function(){return this.depthDelta},getNumPoints:function(){return this.pts.length},printReverse:function(t){t.print("edge "+this.name+": ");for(var e=this.pts.length-1;e>=0;e--)t.print(this.pts[e]+" ");t.println("")},getMonotoneChainEdge:function(){return null===this.mce&&(this.mce=new Zn(this)),this.mce},getEnvelope:function(){if(null===this.env){this.env=new C;for(var t=0;t<this.pts.length;t++)this.env.expandToInclude(this.pts[t])}return this.env},addIntersection:function(t,e,n,i){var r=new g(t.getIntersection(i)),s=e,o=t.getEdgeDistance(n,i),a=s+1;if(a<this.pts.length){var u=this.pts[a];r.equals2D(u)&&(s=a,o=0)}this.eiList.add(r,s,o)},toString:function(){var t=new P;t.append("edge "+this.name+": "),t.append("LINESTRING (");for(var e=0;e<this.pts.length;e++)e>0&&t.append(","),t.append(this.pts[e].x+" "+this.pts[e].y);return t.append(")  "+this.label+" "+this.depthDelta),t.toString()},isPointwiseEqual:function(t){if(this.pts.length!==t.pts.length)return!1;for(var e=0;e<this.pts.length;e++)if(!this.pts[e].equals2D(t.pts[e]))return!1;return!0},setDepthDelta:function(t){this.depthDelta=t},getEdgeIntersectionList:function(){return this.eiList},addIntersections:function(t,e,n){for(var i=0;i<t.getIntersectionNum();i++)this.addIntersection(t,e,n,i)},interfaces_:function(){return[]},getClass:function(){return Jn}}),Jn.updateIM=function(){if(2!==arguments.length)return mn.prototype.updateIM.apply(this,arguments);var t=arguments[0],e=arguments[1];e.setAtLeastIfValid(t.getLocation(0,cn.ON),t.getLocation(1,cn.ON),1),t.isArea()&&(e.setAtLeastIfValid(t.getLocation(0,cn.LEFT),t.getLocation(1,cn.LEFT),2),e.setAtLeastIfValid(t.getLocation(0,cn.RIGHT),t.getLocation(1,cn.RIGHT),2))},h($n,Cn),e($n.prototype,{insertBoundaryPoint:function(t,e){var n=this.nodes.addNode(e),i=n.getLabel(),r=1,s=L.NONE;s=i.getLocation(t,cn.ON),s===L.BOUNDARY&&r++;var o=$n.determineBoundary(this.boundaryNodeRule,r);i.setLocation(t,o)},computeSelfNodes:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.computeSelfNodes(t,e,!1)}if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2],s=new An(n,!0,!1);s.setIsDoneIfProperInt(r);var o=this.createEdgeSetIntersector(),a=this.parentGeom instanceof bt||this.parentGeom instanceof Tt||this.parentGeom instanceof Ot,u=i||!a;return o.computeIntersections(this.edges,s,u),this.addSelfIntersectionNodes(this.argIndex),s}},computeSplitEdges:function(t){for(var e=this.edges.iterator();e.hasNext();){var n=e.next();n.eiList.addSplitEdges(t)}},computeEdgeIntersections:function(t,e,n){var i=new An(e,n,!0);i.setBoundaryNodes(this.getBoundaryNodes(),t.getBoundaryNodes());var r=this.createEdgeSetIntersector();return r.computeIntersections(this.edges,t.edges,i),i},getGeometry:function(){return this.parentGeom},getBoundaryNodeRule:function(){return this.boundaryNodeRule},hasTooFewPoints:function(){return this._hasTooFewPoints},addPoint:function(){if(arguments[0]instanceof Lt){var t=arguments[0],e=t.getCoordinate();this.insertPoint(this.argIndex,e,L.INTERIOR)}else if(arguments[0]instanceof g){var n=arguments[0];this.insertPoint(this.argIndex,n,L.INTERIOR)}},addPolygon:function(t){this.addPolygonRing(t.getExteriorRing(),L.EXTERIOR,L.INTERIOR);for(var e=0;e<t.getNumInteriorRing();e++){var n=t.getInteriorRingN(e);this.addPolygonRing(n,L.INTERIOR,L.EXTERIOR)}},addEdge:function(t){this.insertEdge(t);var e=t.getCoordinates();this.insertPoint(this.argIndex,e[0],L.BOUNDARY),this.insertPoint(this.argIndex,e[e.length-1],L.BOUNDARY)},addLineString:function(t){var e=H.removeRepeatedPoints(t.getCoordinates());if(e.length<2)return this._hasTooFewPoints=!0,this.invalidPoint=e[0],null;var n=new Jn(e,new gn(this.argIndex,L.INTERIOR));this.lineEdgeMap.put(t,n),this.insertEdge(n),f.isTrue(e.length>=2,"found LineString with single point"),this.insertBoundaryPoint(this.argIndex,e[0]),this.insertBoundaryPoint(this.argIndex,e[e.length-1])},getInvalidPoint:function(){return this.invalidPoint},getBoundaryPoints:function(){for(var t=this.getBoundaryNodes(),e=new Array(t.size()).fill(null),n=0,i=t.iterator();i.hasNext();){var r=i.next();e[n++]=r.getCoordinate().copy()}return e},getBoundaryNodes:function(){return null===this.boundaryNodes&&(this.boundaryNodes=this.nodes.getBoundaryNodes(this.argIndex)),this.boundaryNodes},addSelfIntersectionNode:function(t,e,n){return this.isBoundaryNode(t,e)?null:void(n===L.BOUNDARY&&this.useBoundaryDeterminationRule?this.insertBoundaryPoint(t,e):this.insertPoint(t,e,n))},addPolygonRing:function(t,e,n){if(t.isEmpty())return null;var i=H.removeRepeatedPoints(t.getCoordinates());if(i.length<4)return this._hasTooFewPoints=!0,this.invalidPoint=i[0],null;var r=e,s=n;he.isCCW(i)&&(r=n,s=e);var o=new Jn(i,new gn(this.argIndex,L.BOUNDARY,r,s));this.lineEdgeMap.put(t,o),this.insertEdge(o),this.insertPoint(this.argIndex,i[0],L.BOUNDARY)},insertPoint:function(t,e,n){var i=this.nodes.addNode(e),r=i.getLabel();null===r?i.label=new gn(t,n):r.setLocation(t,n)},createEdgeSetIntersector:function(){return new Fn},addSelfIntersectionNodes:function(t){for(var e=this.edges.iterator();e.hasNext();)for(var n=e.next(),i=n.getLabel().getLocation(t),r=n.eiList.iterator();r.hasNext();){var s=r.next();this.addSelfIntersectionNode(t,s.coord,i)}},add:function(){if(1!==arguments.length)return Cn.prototype.add.apply(this,arguments);var t=arguments[0];if(t.isEmpty())return null;if(t instanceof Ot&&(this.useBoundaryDeterminationRule=!1),t instanceof Tt)this.addPolygon(t);else if(t instanceof St)this.addLineString(t);else if(t instanceof Lt)this.addPoint(t);else if(t instanceof Pt)this.addCollection(t);else if(t instanceof gt)this.addCollection(t);else if(t instanceof Ot)this.addCollection(t);else{if(!(t instanceof ft))throw new UnsupportedOperationException(t.getClass().getName());this.addCollection(t)}},addCollection:function(t){for(var e=0;e<t.getNumGeometries();e++){var n=t.getGeometryN(e);this.add(n)}},locate:function(t){return R(this.parentGeom,Rt)&&this.parentGeom.getNumGeometries()>50?(null===this.areaPtLocator&&(this.areaPtLocator=new Un(this.parentGeom)),this.areaPtLocator.locate(t)):this.ptLocator.locate(t,this.parentGeom)},findEdge:function(){if(1===arguments.length){var t=arguments[0];return this.lineEdgeMap.get(t)}return Cn.prototype.findEdge.apply(this,arguments)},interfaces_:function(){return[]},getClass:function(){return $n}}),$n.determineBoundary=function(t,e){return t.isInBoundary(e)?L.BOUNDARY:L.INTERIOR},e(ti.prototype,{getArgGeometry:function(t){return this.arg[t].getGeometry()},setComputationPrecision:function(t){this.resultPrecisionModel=t,this.li.setPrecisionModel(this.resultPrecisionModel)},interfaces_:function(){return[]},getClass:function(){return ti}}),e(ei.prototype,{compareTo:function(t){var e=t,n=ei.compareOriented(this.pts,this._orientation,e.pts,e._orientation);return n},interfaces_:function(){return[s]},getClass:function(){return ei}}),ei.orientation=function(t){return 1===H.increasingDirection(t)},ei.compareOriented=function(t,e,n,i){for(var r=e?1:-1,s=i?1:-1,o=e?t.length:-1,a=i?n.length:-1,u=e?0:t.length-1,l=i?0:n.length-1;;){var h=t[u].compareTo(n[l]);if(0!==h)return h;u+=r,l+=s;var c=u===o,f=l===a;if(c&&!f)return-1;if(!c&&f)return 1;if(c&&f)return 0}},e(ni.prototype,{print:function(t){t.print("MULTILINESTRING ( ");for(var e=0;e<this.edges.size();e++){var n=this.edges.get(e);e>0&&t.print(","),t.print("(");for(var i=n.getCoordinates(),r=0;r<i.length;r++)r>0&&t.print(","),t.print(i[r].x+" "+i[r].y);t.println(")")}t.print(")  ")},addAll:function(t){for(var e=t.iterator();e.hasNext();)this.add(e.next())},findEdgeIndex:function(t){for(var e=0;e<this.edges.size();e++)if(this.edges.get(e).equals(t))return e;return-1},iterator:function(){return this.edges.iterator()},getEdges:function(){return this.edges},get:function(t){return this.edges.get(t)},findEqualEdge:function(t){var e=new ei(t.getCoordinates()),n=this.ocaMap.get(e);return n},add:function(t){this.edges.add(t);var e=new ei(t.getCoordinates());this.ocaMap.put(e,t)},interfaces_:function(){return[]},getClass:function(){return ni}}),h(ii,ti),e(ii.prototype,{insertUniqueEdge:function(t){var e=this.edgeList.findEqualEdge(t);if(null!==e){var n=e.getLabel(),i=t.getLabel();e.isPointwiseEqual(t)||(i=new gn(t.getLabel()),i.flip());var r=e.getDepth();r.isNull()&&r.add(n),r.add(i),n.merge(i)}else this.edgeList.add(t)},getGraph:function(){return this.graph},cancelDuplicateResultEdges:function(){for(var t=this.graph.getEdgeEnds().iterator();t.hasNext();){var e=t.next(),n=e.getSym();e.isInResult()&&n.isInResult()&&(e.setInResult(!1),n.setInResult(!1))}},isCoveredByLA:function(t){return this.isCovered(t,this.resultLineList)?!0:!!this.isCovered(t,this.resultPolyList)},computeGeometry:function(t,e,n,i){var r=new I;return r.addAll(t),r.addAll(e),r.addAll(n),r.isEmpty()?ii.createEmptyResult(i,this.arg[0].getGeometry(),this.arg[1].getGeometry(),this.geomFact):this.geomFact.buildGeometry(r)},mergeSymLabels:function(){for(var t=this.graph.getNodes().iterator();t.hasNext();){var e=t.next();e.getEdges().mergeSymLabels()}},isCovered:function(t,e){for(var n=e.iterator();n.hasNext();){var i=n.next(),r=this.ptLocator.locate(t,i);if(r!==L.EXTERIOR)return!0}return!1},replaceCollapsedEdges:function(){for(var t=new I,e=this.edgeList.iterator();e.hasNext();){var n=e.next();n.isCollapsed()&&(e.remove(),t.add(n.getCollapsedEdge()))}this.edgeList.addAll(t)},updateNodeLabelling:function(){for(var t=this.graph.getNodes().iterator();t.hasNext();){var e=t.next(),n=e.getEdges().getLabel();e.getLabel().merge(n)}},getResultGeometry:function(t){return this.computeOverlay(t),this.resultGeom},insertUniqueEdges:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.insertUniqueEdge(n)}},computeOverlay:function(t){this.copyPoints(0),this.copyPoints(1),this.arg[0].computeSelfNodes(this.li,!1),this.arg[1].computeSelfNodes(this.li,!1),this.arg[0].computeEdgeIntersections(this.arg[1],this.li,!0);var e=new I;this.arg[0].computeSplitEdges(e),this.arg[1].computeSplitEdges(e);this.insertUniqueEdges(e),this.computeLabelsFromDepths(),this.replaceCollapsedEdges(),ln.checkValid(this.edgeList.getEdges()),this.graph.addEdges(this.edgeList.getEdges()),this.computeLabelling(),this.labelIncompleteNodes(),this.findResultAreaEdges(t),this.cancelDuplicateResultEdges();var n=new Sn(this.geomFact);n.add(this.graph),this.resultPolyList=n.getPolygons();var i=new wn(this,this.geomFact,this.ptLocator);this.resultLineList=i.build(t);var r=new Ln(this,this.geomFact,this.ptLocator);this.resultPointList=r.build(t),
this.resultGeom=this.computeGeometry(this.resultPointList,this.resultLineList,this.resultPolyList,t)},labelIncompleteNode:function(t,e){var n=this.ptLocator.locate(t.getCoordinate(),this.arg[e].getGeometry());t.getLabel().setLocation(e,n)},copyPoints:function(t){for(var e=this.arg[t].getNodeIterator();e.hasNext();){var n=e.next(),i=this.graph.addNode(n.getCoordinate());i.setLabel(t,n.getLabel().getLocation(t))}},findResultAreaEdges:function(t){for(var e=this.graph.getEdgeEnds().iterator();e.hasNext();){var n=e.next(),i=n.getLabel();i.isArea()&&!n.isInteriorAreaEdge()&&ii.isResultOfOp(i.getLocation(0,cn.RIGHT),i.getLocation(1,cn.RIGHT),t)&&n.setInResult(!0)}},computeLabelsFromDepths:function(){for(var t=this.edgeList.iterator();t.hasNext();){var e=t.next(),n=e.getLabel(),i=e.getDepth();if(!i.isNull()){i.normalize();for(var r=0;2>r;r++)n.isNull(r)||!n.isArea()||i.isNull(r)||(0===i.getDelta(r)?n.toLine(r):(f.isTrue(!i.isNull(r,cn.LEFT),"depth of LEFT side has not been initialized"),n.setLocation(r,cn.LEFT,i.getLocation(r,cn.LEFT)),f.isTrue(!i.isNull(r,cn.RIGHT),"depth of RIGHT side has not been initialized"),n.setLocation(r,cn.RIGHT,i.getLocation(r,cn.RIGHT))))}}},computeLabelling:function(){for(var t=this.graph.getNodes().iterator();t.hasNext();){var e=t.next();e.getEdges().computeLabelling(this.arg)}this.mergeSymLabels(),this.updateNodeLabelling()},labelIncompleteNodes:function(){for(var t=0,e=this.graph.getNodes().iterator();e.hasNext();){var n=e.next(),i=n.getLabel();n.isIsolated()&&(t++,i.isNull(0)?this.labelIncompleteNode(n,0):this.labelIncompleteNode(n,1)),n.getEdges().updateLabelling(i)}},isCoveredByA:function(t){return!!this.isCovered(t,this.resultPolyList)},interfaces_:function(){return[]},getClass:function(){return ii}}),ii.overlayOp=function(t,e,n){var i=new ii(t,e),r=i.getResultGeometry(n);return r},ii.intersection=function(t,e){if(t.isEmpty()||e.isEmpty())return ii.createEmptyResult(ii.INTERSECTION,t,e,t.getFactory());if(t.isGeometryCollection()){var n=e;return hn.map(t,{interfaces_:function(){return[MapOp]},map:function(t){return t.intersection(n)}})}return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),si.overlayOp(t,e,ii.INTERSECTION)},ii.symDifference=function(t,e){if(t.isEmpty()||e.isEmpty()){if(t.isEmpty()&&e.isEmpty())return ii.createEmptyResult(ii.SYMDIFFERENCE,t,e,t.getFactory());if(t.isEmpty())return e.copy();if(e.isEmpty())return t.copy()}return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),si.overlayOp(t,e,ii.SYMDIFFERENCE)},ii.resultDimension=function(t,e,n){var i=e.getDimension(),r=n.getDimension(),s=-1;switch(t){case ii.INTERSECTION:s=Math.min(i,r);break;case ii.UNION:s=Math.max(i,r);break;case ii.DIFFERENCE:s=i;break;case ii.SYMDIFFERENCE:s=Math.max(i,r)}return s},ii.createEmptyResult=function(t,e,n,i){var r=null;switch(ii.resultDimension(t,e,n)){case-1:r=i.createGeometryCollection(new Array(0).fill(null));break;case 0:r=i.createPoint();break;case 1:r=i.createLineString();break;case 2:r=i.createPolygon()}return r},ii.difference=function(t,e){return t.isEmpty()?ii.createEmptyResult(ii.DIFFERENCE,t,e,t.getFactory()):e.isEmpty()?t.copy():(t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),si.overlayOp(t,e,ii.DIFFERENCE))},ii.isResultOfOp=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=t.getLocation(0),i=t.getLocation(1);return ii.isResultOfOp(n,i,e)}if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];switch(r===L.BOUNDARY&&(r=L.INTERIOR),s===L.BOUNDARY&&(s=L.INTERIOR),o){case ii.INTERSECTION:return r===L.INTERIOR&&s===L.INTERIOR;case ii.UNION:return r===L.INTERIOR||s===L.INTERIOR;case ii.DIFFERENCE:return r===L.INTERIOR&&s!==L.INTERIOR;case ii.SYMDIFFERENCE:return r===L.INTERIOR&&s!==L.INTERIOR||r!==L.INTERIOR&&s===L.INTERIOR}return!1}},ii.INTERSECTION=1,ii.UNION=2,ii.DIFFERENCE=3,ii.SYMDIFFERENCE=4,e(ri.prototype,{selfSnap:function(t){var e=new Ie(t),n=e.snapTo(t,this.snapTolerance);return n},removeCommonBits:function(t){this.cbr=new Se,this.cbr.add(t[0]),this.cbr.add(t[1]);var e=new Array(2).fill(null);return e[0]=this.cbr.removeCommonBits(t[0].copy()),e[1]=this.cbr.removeCommonBits(t[1].copy()),e},prepareResult:function(t){return this.cbr.addCommonBits(t),t},getResultGeometry:function(t){var e=this.snap(this.geom),n=ii.overlayOp(e[0],e[1],t);return this.prepareResult(n)},checkValid:function(t){t.isValid()||A.out.println("Snapped geometry is invalid")},computeSnapTolerance:function(){this.snapTolerance=Ie.computeOverlaySnapTolerance(this.geom[0],this.geom[1])},snap:function(t){var e=this.removeCommonBits(t),n=Ie.snap(e[0],e[1],this.snapTolerance);return n},interfaces_:function(){return[]},getClass:function(){return ri}}),ri.overlayOp=function(t,e,n){var i=new ri(t,e);return i.getResultGeometry(n)},ri.union=function(t,e){return ri.overlayOp(t,e,ii.UNION)},ri.intersection=function(t,e){return ri.overlayOp(t,e,ii.INTERSECTION)},ri.symDifference=function(t,e){return ri.overlayOp(t,e,ii.SYMDIFFERENCE)},ri.difference=function(t,e){return ri.overlayOp(t,e,ii.DIFFERENCE)},e(si.prototype,{getResultGeometry:function(t){var e=null,n=!1,i=null;try{e=ii.overlayOp(this.geom[0],this.geom[1],t);var r=!0;r&&(n=!0)}catch(t){if(!(t instanceof l))throw t;i=t}finally{}if(!n)try{e=ri.overlayOp(this.geom[0],this.geom[1],t)}catch(t){throw t instanceof l?i:t}finally{}return e},interfaces_:function(){return[]},getClass:function(){return si}}),si.overlayOp=function(t,e,n){var i=new si(t,e);return i.getResultGeometry(n)},si.union=function(t,e){return si.overlayOp(t,e,ii.UNION)},si.intersection=function(t,e){return si.overlayOp(t,e,ii.INTERSECTION)},si.symDifference=function(t,e){return si.overlayOp(t,e,ii.SYMDIFFERENCE)},si.difference=function(t,e){return si.overlayOp(t,e,ii.DIFFERENCE)},e(oi.prototype,{addPolygon:function(t){if(t.isEmpty())return null;var e=null,n=0,i=this.horizontalBisector(t);if(0===i.getLength())n=0,e=i.getCoordinate();else{var r=si.overlayOp(i,t,ii.INTERSECTION),s=this.widestGeometry(r);n=s.getEnvelopeInternal().getWidth(),e=oi.centre(s.getEnvelopeInternal())}(null===this.interiorPoint||n>this.maxWidth)&&(this.interiorPoint=e,this.maxWidth=n)},getInteriorPoint:function(){return this.interiorPoint},widestGeometry:function t(){if(arguments[0]instanceof ft){var e=arguments[0];if(e.isEmpty())return e;for(var t=e.getGeometryN(0),n=1;n<e.getNumGeometries();n++)e.getGeometryN(n).getEnvelopeInternal().getWidth()>t.getEnvelopeInternal().getWidth()&&(t=e.getGeometryN(n));return t}if(arguments[0]instanceof B){var i=arguments[0];return i instanceof ft?this.widestGeometry(i):i}},horizontalBisector:function(t){var e=t.getEnvelopeInternal(),n=ai.getBisectorY(t);return this.factory.createLineString([new g(e.getMinX(),n),new g(e.getMaxX(),n)])},add:function(t){if(t instanceof Tt)this.addPolygon(t);else if(t instanceof ft)for(var e=t,n=0;n<e.getNumGeometries();n++)this.add(e.getGeometryN(n))},interfaces_:function(){return[]},getClass:function(){return oi}}),oi.centre=function(t){return new g(oi.avg(t.getMinX(),t.getMaxX()),oi.avg(t.getMinY(),t.getMaxY()))},oi.avg=function(t,e){return(t+e)/2},e(ai.prototype,{updateInterval:function(t){t<=this.centreY?t>this.loY&&(this.loY=t):t>this.centreY&&t<this.hiY&&(this.hiY=t)},getBisectorY:function(){this.process(this.poly.getExteriorRing());for(var t=0;t<this.poly.getNumInteriorRing();t++)this.process(this.poly.getInteriorRingN(t));var e=oi.avg(this.hiY,this.loY);return e},process:function(t){for(var e=t.getCoordinateSequence(),n=0;n<e.size();n++){var i=e.getY(n);this.updateInterval(i)}},interfaces_:function(){return[]},getClass:function(){return ai}}),ai.getBisectorY=function(t){var e=new ai(t);return e.getBisectorY()},oi.SafeBisectorFinder=ai,e(ui.prototype,{addEndpoints:function(){if(arguments[0]instanceof B){var t=arguments[0];if(t instanceof St)this.addEndpoints(t.getCoordinates());else if(t instanceof ft)for(var e=t,n=0;n<e.getNumGeometries();n++)this.addEndpoints(e.getGeometryN(n))}else if(arguments[0]instanceof Array){var i=arguments[0];this.add(i[0]),this.add(i[i.length-1])}},getInteriorPoint:function(){return this.interiorPoint},addInterior:function(){if(arguments[0]instanceof B){var t=arguments[0];if(t instanceof St)this.addInterior(t.getCoordinates());else if(t instanceof ft)for(var e=t,n=0;n<e.getNumGeometries();n++)this.addInterior(e.getGeometryN(n))}else if(arguments[0]instanceof Array)for(var i=arguments[0],n=1;n<i.length-1;n++)this.add(i[n])},add:function(t){var e=t.distance(this.centroid);e<this.minDistance&&(this.interiorPoint=new g(t),this.minDistance=e)},interfaces_:function(){return[]},getClass:function(){return ui}}),e(li.prototype,{getInteriorPoint:function(){return this.interiorPoint},add:function(){if(arguments[0]instanceof B){var t=arguments[0];if(t instanceof Lt)this.add(t.getCoordinate());else if(t instanceof ft)for(var e=t,n=0;n<e.getNumGeometries();n++)this.add(e.getGeometryN(n))}else if(arguments[0]instanceof g){var i=arguments[0],r=i.distance(this.centroid);r<this.minDistance&&(this.interiorPoint=new g(i),this.minDistance=r)}},interfaces_:function(){return[]},getClass:function(){return li}}),e(hi.prototype,{interfaces_:function(){return[]},getClass:function(){return hi}}),hi.toDegrees=function(t){return 180*t/Math.PI},hi.normalize=function(t){for(;t>Math.PI;)t-=hi.PI_TIMES_2;for(;t<=-Math.PI;)t+=hi.PI_TIMES_2;return t},hi.angle=function(){if(1===arguments.length){var t=arguments[0];return Math.atan2(t.y,t.x)}if(2===arguments.length){var e=arguments[0],n=arguments[1],i=n.x-e.x,r=n.y-e.y;return Math.atan2(r,i)}},hi.isAcute=function(t,e,n){var i=t.x-e.x,r=t.y-e.y,s=n.x-e.x,o=n.y-e.y,a=i*s+r*o;return a>0},hi.isObtuse=function(t,e,n){var i=t.x-e.x,r=t.y-e.y,s=n.x-e.x,o=n.y-e.y,a=i*s+r*o;return 0>a},hi.interiorAngle=function(t,e,n){var i=hi.angle(e,t),r=hi.angle(e,n);return Math.abs(r-i)},hi.normalizePositive=function(t){if(0>t){for(;0>t;)t+=hi.PI_TIMES_2;t>=hi.PI_TIMES_2&&(t=0)}else{for(;t>=hi.PI_TIMES_2;)t-=hi.PI_TIMES_2;0>t&&(t=0)}return t},hi.angleBetween=function(t,e,n){var i=hi.angle(e,t),r=hi.angle(e,n);return hi.diff(i,r)},hi.diff=function(t,e){var n=null;return n=e>t?e-t:t-e,n>Math.PI&&(n=2*Math.PI-n),n},hi.toRadians=function(t){return t*Math.PI/180},hi.getTurn=function(t,e){var n=Math.sin(e-t);return n>0?hi.COUNTERCLOCKWISE:0>n?hi.CLOCKWISE:hi.NONE},hi.angleBetweenOriented=function(t,e,n){var i=hi.angle(e,t),r=hi.angle(e,n),s=r-i;return s<=-Math.PI?s+hi.PI_TIMES_2:s>Math.PI?s-hi.PI_TIMES_2:s},hi.PI_TIMES_2=2*Math.PI,hi.PI_OVER_2=Math.PI/2,hi.PI_OVER_4=Math.PI/4,hi.COUNTERCLOCKWISE=he.COUNTERCLOCKWISE,hi.CLOCKWISE=he.CLOCKWISE,hi.NONE=he.COLLINEAR,e(ci.prototype,{area:function(){return ci.area(this.p0,this.p1,this.p2)},signedArea:function(){return ci.signedArea(this.p0,this.p1,this.p2)},interpolateZ:function(t){if(null===t)throw new i("Supplied point is null.");return ci.interpolateZ(t,this.p0,this.p1,this.p2)},longestSideLength:function(){return ci.longestSideLength(this.p0,this.p1,this.p2)},isAcute:function(){return ci.isAcute(this.p0,this.p1,this.p2)},circumcentre:function(){return ci.circumcentre(this.p0,this.p1,this.p2)},area3D:function(){return ci.area3D(this.p0,this.p1,this.p2)},centroid:function(){return ci.centroid(this.p0,this.p1,this.p2)},inCentre:function(){return ci.inCentre(this.p0,this.p1,this.p2)},interfaces_:function(){return[]},getClass:function(){return ci}}),ci.area=function(t,e,n){return Math.abs(((n.x-t.x)*(e.y-t.y)-(e.x-t.x)*(n.y-t.y))/2)},ci.signedArea=function(t,e,n){return((n.x-t.x)*(e.y-t.y)-(e.x-t.x)*(n.y-t.y))/2},ci.det=function(t,e,n,i){return t*i-e*n},ci.interpolateZ=function(t,e,n,i){var r=e.x,s=e.y,o=n.x-r,a=i.x-r,u=n.y-s,l=i.y-s,h=o*l-a*u,c=t.x-r,f=t.y-s,g=(l*c-a*f)/h,d=(-u*c+o*f)/h,p=e.z+g*(n.z-e.z)+d*(i.z-e.z);return p},ci.longestSideLength=function(t,e,n){var i=t.distance(e),r=e.distance(n),s=n.distance(t),o=i;return r>o&&(o=r),s>o&&(o=s),o},ci.isAcute=function(t,e,n){return hi.isAcute(t,e,n)&&hi.isAcute(e,n,t)?!!hi.isAcute(n,t,e):!1},ci.circumcentre=function(t,e,n){var i=n.x,r=n.y,s=t.x-i,o=t.y-r,a=e.x-i,u=e.y-r,l=2*ci.det(s,o,a,u),h=ci.det(o,s*s+o*o,u,a*a+u*u),c=ci.det(s,s*s+o*o,a,a*a+u*u),f=i-h/l,d=r+c/l;return new g(f,d)},ci.perpendicularBisector=function(t,e){var n=e.x-t.x,i=e.y-t.y,r=new F(t.x+n/2,t.y+i/2,1),s=new F(t.x-i+n/2,t.y+n+i/2,1);return new F(r,s)},ci.angleBisector=function(t,e,n){var i=e.distance(t),r=e.distance(n),s=i/(i+r),o=n.x-t.x,a=n.y-t.y,u=new g(t.x+s*o,t.y+s*a);return u},ci.area3D=function(t,e,n){var i=e.x-t.x,r=e.y-t.y,s=e.z-t.z,o=n.x-t.x,a=n.y-t.y,u=n.z-t.z,l=r*u-s*a,h=s*o-i*u,c=i*a-r*o,f=l*l+h*h+c*c,g=Math.sqrt(f)/2;return g},ci.centroid=function(t,e,n){var i=(t.x+e.x+n.x)/3,r=(t.y+e.y+n.y)/3;return new g(i,r)},ci.inCentre=function(t,e,n){var i=e.distance(n),r=t.distance(n),s=t.distance(e),o=i+r+s,a=(i*t.x+r*e.x+s*n.x)/o,u=(i*t.y+r*e.y+s*n.y)/o;return new g(a,u)},e(fi.prototype,{getRadius:function(){return this.compute(),this.radius},getDiameter:function(){switch(this.compute(),this.extremalPts.length){case 0:return this.input.getFactory().createLineString();case 1:return this.input.getFactory().createPoint(this.centre)}var t=this.extremalPts[0],e=this.extremalPts[1];return this.input.getFactory().createLineString([t,e])},getExtremalPoints:function(){return this.compute(),this.extremalPts},computeCirclePoints:function(){if(this.input.isEmpty())return this.extremalPts=new Array(0).fill(null),null;if(1===this.input.getNumPoints()){var t=this.input.getCoordinates();return this.extremalPts=[new g(t[0])],null}var e=this.input.convexHull(),n=e.getCoordinates(),t=n;if(n[0].equals2D(n[n.length-1])&&(t=new Array(n.length-1).fill(null),H.copyDeep(n,0,t,0,n.length-1)),t.length<=2)return this.extremalPts=H.copyDeep(t),null;for(var i=fi.lowestPoint(t),r=fi.pointWitMinAngleWithX(t,i),s=0;s<t.length;s++){var o=fi.pointWithMinAngleWithSegment(t,i,r);if(hi.isObtuse(i,o,r))return this.extremalPts=[new g(i),new g(r)],null;if(hi.isObtuse(o,i,r))i=o;else{if(!hi.isObtuse(o,r,i))return this.extremalPts=[new g(i),new g(r),new g(o)],null;r=o}}f.shouldNeverReachHere("Logic failure in Minimum Bounding Circle algorithm!")},compute:function(){return null!==this.extremalPts?null:(this.computeCirclePoints(),this.computeCentre(),void(null!==this.centre&&(this.radius=this.centre.distance(this.extremalPts[0]))))},getFarthestPoints:function(){switch(this.compute(),this.extremalPts.length){case 0:return this.input.getFactory().createLineString();case 1:return this.input.getFactory().createPoint(this.centre)}var t=this.extremalPts[0],e=this.extremalPts[this.extremalPts.length-1];return this.input.getFactory().createLineString([t,e])},getCircle:function(){if(this.compute(),null===this.centre)return this.input.getFactory().createPolygon();var t=this.input.getFactory().createPoint(this.centre);return 0===this.radius?t:t.buffer(this.radius)},getCentre:function(){return this.compute(),this.centre},computeCentre:function(){switch(this.extremalPts.length){case 0:this.centre=null;break;case 1:this.centre=this.extremalPts[0];break;case 2:this.centre=new g((this.extremalPts[0].x+this.extremalPts[1].x)/2,(this.extremalPts[0].y+this.extremalPts[1].y)/2);break;case 3:this.centre=ci.circumcentre(this.extremalPts[0],this.extremalPts[1],this.extremalPts[2])}},interfaces_:function(){return[]},getClass:function(){return fi}}),fi.pointWitMinAngleWithX=function(t,e){for(var n=r.MAX_VALUE,i=null,s=0;s<t.length;s++){var o=t[s];if(o!==e){var a=o.x-e.x,u=o.y-e.y;0>u&&(u=-u);var l=Math.sqrt(a*a+u*u),h=u/l;n>h&&(n=h,i=o)}}return i},fi.lowestPoint=function(t){for(var e=t[0],n=1;n<t.length;n++)t[n].y<e.y&&(e=t[n]);return e},fi.pointWithMinAngleWithSegment=function(t,e,n){for(var i=r.MAX_VALUE,s=null,o=0;o<t.length;o++){var a=t[o];if(a!==e&&a!==n){var u=hi.angleBetween(e,a,n);i>u&&(i=u,s=a)}}return s},e(gi.prototype,{getWidthCoordinate:function(){return this.computeMinimumDiameter(),this.minWidthPt},getSupportingSegment:function(){return this.computeMinimumDiameter(),this.inputGeom.getFactory().createLineString([this.minBaseSeg.p0,this.minBaseSeg.p1])},getDiameter:function(){if(this.computeMinimumDiameter(),null===this.minWidthPt)return this.inputGeom.getFactory().createLineString(null);var t=this.minBaseSeg.project(this.minWidthPt);return this.inputGeom.getFactory().createLineString([t,this.minWidthPt])},computeWidthConvex:function(t){t instanceof Tt?this.convexHullPts=t.getExteriorRing().getCoordinates():this.convexHullPts=t.getCoordinates(),0===this.convexHullPts.length?(this.minWidth=0,this.minWidthPt=null,this.minBaseSeg=null):1===this.convexHullPts.length?(this.minWidth=0,this.minWidthPt=this.convexHullPts[0],this.minBaseSeg.p0=this.convexHullPts[0],this.minBaseSeg.p1=this.convexHullPts[0]):2===this.convexHullPts.length||3===this.convexHullPts.length?(this.minWidth=0,this.minWidthPt=this.convexHullPts[0],this.minBaseSeg.p0=this.convexHullPts[0],this.minBaseSeg.p1=this.convexHullPts[1]):this.computeConvexRingMinDiameter(this.convexHullPts)},computeConvexRingMinDiameter:function(t){this.minWidth=r.MAX_VALUE;for(var e=1,n=new ce,i=0;i<t.length-1;i++)n.p0=t[i],n.p1=t[i+1],e=this.findMaxPerpDistance(t,n,e)},computeMinimumDiameter:function(){if(null!==this.minWidthPt)return null;if(this.isConvex)this.computeWidthConvex(this.inputGeom);else{var t=new me(this.inputGeom).getConvexHull();this.computeWidthConvex(t)}},getLength:function(){return this.computeMinimumDiameter(),this.minWidth},findMaxPerpDistance:function(t,e,n){for(var i=e.distancePerpendicular(t[n]),r=i,s=n,o=s;r>=i;)i=r,s=o,o=gi.nextIndex(t,s),r=e.distancePerpendicular(t[o]);return i<this.minWidth&&(this.minPtIndex=s,this.minWidth=i,this.minWidthPt=t[this.minPtIndex],this.minBaseSeg=new ce(e)),s},getMinimumRectangle:function(){if(this.computeMinimumDiameter(),0===this.minWidth)return this.minBaseSeg.p0.equals2D(this.minBaseSeg.p1)?this.inputGeom.getFactory().createPoint(this.minBaseSeg.p0):this.minBaseSeg.toGeometry(this.inputGeom.getFactory());for(var t=this.minBaseSeg.p1.x-this.minBaseSeg.p0.x,e=this.minBaseSeg.p1.y-this.minBaseSeg.p0.y,n=r.MAX_VALUE,i=-r.MAX_VALUE,s=r.MAX_VALUE,o=-r.MAX_VALUE,a=0;a<this.convexHullPts.length;a++){var u=gi.computeC(t,e,this.convexHullPts[a]);u>i&&(i=u),n>u&&(n=u);var l=gi.computeC(-e,t,this.convexHullPts[a]);l>o&&(o=l),s>l&&(s=l)}var h=gi.computeSegmentForLine(-t,-e,o),c=gi.computeSegmentForLine(-t,-e,s),f=gi.computeSegmentForLine(-e,t,i),g=gi.computeSegmentForLine(-e,t,n),d=f.lineIntersection(h),p=g.lineIntersection(h),v=g.lineIntersection(c),m=f.lineIntersection(c),y=this.inputGeom.getFactory().createLinearRing([d,p,v,m,d]);return this.inputGeom.getFactory().createPolygon(y,null)},interfaces_:function(){return[]},getClass:function(){return gi}}),gi.nextIndex=function(t,e){return e++,e>=t.length&&(e=0),e},gi.computeC=function(t,e,n){return t*n.y-e*n.x},gi.getMinimumDiameter=function(t){return new gi(t).getDiameter()},gi.getMinimumRectangle=function(t){return new gi(t).getMinimumRectangle()},gi.computeSegmentForLine=function(t,e,n){var i=null,r=null;return Math.abs(e)>Math.abs(t)?(i=new g(0,n/e),r=new g(1,n/e-t/e)):(i=new g(n/t,0),r=new g(n/t-e/t,1)),new ce(i,r)};var co=Object.freeze({Centroid:ge,CGAlgorithms:he,ConvexHull:me,InteriorPointArea:oi,InteriorPointLine:ui,InteriorPointPoint:li,RobustLineIntersector:ae,MinimumBoundingCircle:fi,MinimumDiameter:gi});e(di.prototype,{getResultGeometry:function(){return new pi(this.distanceTolerance).transform(this.inputGeom)},setDistanceTolerance:function(t){if(0>=t)throw new i("Tolerance must be positive");this.distanceTolerance=t},interfaces_:function(){return[]},getClass:function(){return di}}),di.densifyPoints=function(t,e,n){for(var i=new ce,r=new N,s=0;s<t.length-1;s++){i.p0=t[s],i.p1=t[s+1],r.add(i.p0,!1);var o=i.getLength(),a=Math.trunc(o/e)+1;if(a>1)for(var u=o/a,l=1;a>l;l++){var h=l*u/o,c=i.pointAlong(h);n.makePrecise(c),r.add(c,!1)}}return r.add(t[t.length-1],!1),r.toCoordinateArray()},di.densify=function(t,e){var n=new di(t);return n.setDistanceTolerance(e),n.getResultGeometry()},h(pi,xe),e(pi.prototype,{transformMultiPolygon:function(t,e){var n=xe.prototype.transformMultiPolygon.call(this,t,e);return this.createValidArea(n)},transformPolygon:function(t,e){var n=xe.prototype.transformPolygon.call(this,t,e);return e instanceof Ot?n:this.createValidArea(n)},transformCoordinates:function(t,e){var n=t.toCoordinateArray(),i=di.densifyPoints(n,this.distanceTolerance,e.getPrecisionModel());return e instanceof St&&1===i.length&&(i=new Array(0).fill(null)),this.factory.getCoordinateSequenceFactory().create(i)},createValidArea:function(t){return t.buffer(0)},interfaces_:function(){return[]},getClass:function(){return pi}}),di.DensifyTransformer=pi;var fo=Object.freeze({Densifier:di});e(vi.prototype,{find:function(t){var e=this;do{if(null===e)return null;if(e.dest().equals2D(t))return e;e=e.oNext()}while(e!==this);return null},dest:function(){return this._sym._orig},oNext:function(){return this._sym._next},insert:function(t){if(this.oNext()===this)return this.insertAfter(t),null;var e=this.compareTo(t),n=this;do{var i=n.oNext(),r=i.compareTo(t);if(r!==e||i===this)return n.insertAfter(t),null;n=i}while(n!==this);f.shouldNeverReachHere()},insertAfter:function(t){f.equals(this._orig,t.orig());var e=this.oNext();this._sym.setNext(t),t.sym().setNext(e)},degree:function t(){var t=0,e=this;do t++,e=e.oNext();while(e!==this);return t},equals:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this._orig.equals2D(t)&&this._sym._orig.equals(e)}},deltaY:function(){return this._sym._orig.y-this._orig.y},sym:function(){return this._sym},prev:function(){return this._sym.next()._sym},compareAngularDirection:function(t){var e=this.deltaX(),n=this.deltaY(),i=t.deltaX(),r=t.deltaY();if(e===i&&n===r)return 0;var s=Je.quadrant(e,n),o=Je.quadrant(i,r);return s>o?1:o>s?-1:he.computeOrientation(t._orig,t.dest(),this.dest())},prevNode:function(){for(var t=this;2===t.degree();)if(t=t.prev(),t===this)return null;return t},compareTo:function(t){var e=t,n=this.compareAngularDirection(e);return n},next:function(){return this._next},setSym:function(t){this._sym=t},orig:function(){return this._orig},toString:function(){return"HE("+this._orig.x+" "+this._orig.y+", "+this._sym._orig.x+" "+this._sym._orig.y+")"},setNext:function(t){this._next=t},init:function(t){this.setSym(t),t.setSym(this),this.setNext(t),t.setNext(this)},deltaX:function(){return this._sym._orig.x-this._orig.x},interfaces_:function(){return[]},getClass:function(){return vi}}),vi.init=function(t,e){if(null!==t._sym||null!==e._sym||null!==t._next||null!==e._next)throw new IllegalStateException("Edges are already initialized");return t.init(e),t},vi.create=function(t,e){var n=new vi(t),i=new vi(e);return n.init(i),n},h(mi,vi),e(mi.prototype,{mark:function(){this._isMarked=!0},setMark:function(t){this._isMarked=t},isMarked:function(){return this._isMarked},interfaces_:function(){return[]},getClass:function(){return mi}}),mi.setMarkBoth=function(t,e){t.setMark(e),t.sym().setMark(e)},mi.isMarked=function(t){return t.isMarked()},mi.setMark=function(t,e){t.setMark(e)},mi.markBoth=function(t){t.mark(),t.sym().mark()},mi.mark=function(t){t.mark()},e(yi.prototype,{insert:function(t,e,n){var i=this.create(t,e);null!==n?n.insert(i):this.vertexMap.put(t,i);var r=this.vertexMap.get(e);return null!==r?r.insert(i.sym()):this.vertexMap.put(e,i.sym()),i},create:function(t,e){var n=this.createEdge(t),i=this.createEdge(e);return vi.init(n,i),n},createEdge:function(t){return new vi(t)},addEdge:function(t,e){if(!yi.isValidEdge(t,e))return null;var n=this.vertexMap.get(t),i=null;if(null!==n&&(i=n.find(e)),null!==i)return i;var r=this.insert(t,e,n);return r},getVertexEdges:function(){return this.vertexMap.values()},findEdge:function(t,e){var n=this.vertexMap.get(t);return null===n?null:n.find(e)},interfaces_:function(){return[]},getClass:function(){return yi}}),yi.isValidEdge=function(t,e){var n=e.compareTo(t);return 0!==n},h(xi,mi),e(xi.prototype,{setStart:function(){this._isStart=!0},isStart:function(){return this._isStart},interfaces_:function(){return[]},getClass:function(){return xi}}),h(Ei,yi),e(Ei.prototype,{createEdge:function(t){return new xi(t)},interfaces_:function(){return[]},getClass:function(){return Ei}}),e(Ii.prototype,{addLine:function(t){this.lines.add(this.factory.createLineString(t.toCoordinateArray()))},updateRingStartEdge:function(t){return t.isStart()||(t=t.sym(),t.isStart())?null===this.ringStartEdge?(this.ringStartEdge=t,null):void(t.orig().compareTo(this.ringStartEdge.orig())<0&&(this.ringStartEdge=t)):null},getResult:function(){return null===this.result&&this.computeResult(),this.result},process:function(t){var e=t.prevNode();null===e&&(e=t),this.stackEdges(e),this.buildLines()},buildRing:function(t){var e=new N,n=t;for(e.add(n.orig().copy(),!1);2===n.sym().degree();){var i=n.next();if(i===t)break;e.add(i.orig().copy(),!1),n=i}e.add(n.dest().copy(),!1),this.addLine(e)},buildLine:function(t){var e=new N,n=t;for(this.ringStartEdge=null,mi.markBoth(n),e.add(n.orig().copy(),!1);2===n.sym().degree();){this.updateRingStartEdge(n);var i=n.next();if(i===t)return this.buildRing(this.ringStartEdge),null;e.add(i.orig().copy(),!1),n=i,mi.markBoth(n)}e.add(n.dest().copy(),!1),this.stackEdges(n.sym()),this.addLine(e)},stackEdges:function(t){var e=t;do mi.isMarked(e)||this.nodeEdgeStack.add(e),e=e.oNext();while(e!==t)},computeResult:function(){for(var t=this.graph.getVertexEdges(),e=t.iterator();e.hasNext();){var n=e.next();mi.isMarked(n)||this.process(n)}this.result=this.factory.buildGeometry(this.lines)},buildLines:function(){for(;!this.nodeEdgeStack.empty();){var t=this.nodeEdgeStack.pop();mi.isMarked(t)||this.buildLine(t)}},add:function(){if(arguments[0]instanceof B){var t=arguments[0];t.apply({interfaces_:function(){return[q]},filter:function(t){t instanceof St&&this.add(t)}})}else if(R(arguments[0],v))for(var e=arguments[0],n=e.iterator();n.hasNext();){var i=n.next();this.add(i)}else if(arguments[0]instanceof St){var r=arguments[0];null===this.factory&&(this.factory=r.getFactory());for(var s=r.getCoordinateSequence(),o=!1,n=1;n<s.size();n++){var a=this.graph.addEdge(s.getCoordinate(n-1),s.getCoordinate(n));null!==a&&(o||(a.setStart(),o=!0))}}},interfaces_:function(){return[]},getClass:function(){return Ii}}),Ii.dissolve=function(t){var e=new Ii;return e.add(t),e.getResult()};var go=Object.freeze({LineDissolver:Ii});e(Ni.prototype,{hasChildren:function(){for(var t=0;4>t;t++)if(null!==this.subnode[t])return!0;return!1},isPrunable:function(){return!(this.hasChildren()||this.hasItems())},addAllItems:function(t){t.addAll(this.items);for(var e=0;4>e;e++)null!==this.subnode[e]&&this.subnode[e].addAllItems(t);return t},getNodeCount:function(){for(var t=0,e=0;4>e;e++)null!==this.subnode[e]&&(t+=this.subnode[e].size());return t+1},size:function(){for(var t=0,e=0;4>e;e++)null!==this.subnode[e]&&(t+=this.subnode[e].size());return t+this.items.size()},addAllItemsFromOverlapping:function(t,e){if(!this.isSearchMatch(t))return null;e.addAll(this.items);for(var n=0;4>n;n++)null!==this.subnode[n]&&this.subnode[n].addAllItemsFromOverlapping(t,e)},visitItems:function(t,e){for(var n=this.items.iterator();n.hasNext();)e.visitItem(n.next())},hasItems:function(){return!this.items.isEmpty()},remove:function(t,e){if(!this.isSearchMatch(t))return!1;for(var n=!1,i=0;4>i;i++)if(null!==this.subnode[i]&&(n=this.subnode[i].remove(t,e))){this.subnode[i].isPrunable()&&(this.subnode[i]=null);break}return n?n:n=this.items.remove(e)},visit:function(t,e){if(!this.isSearchMatch(t))return null;this.visitItems(t,e);for(var n=0;4>n;n++)null!==this.subnode[n]&&this.subnode[n].visit(t,e)},getItems:function(){return this.items},depth:function(){for(var t=0,e=0;4>e;e++)if(null!==this.subnode[e]){var n=this.subnode[e].depth();n>t&&(t=n)}return t+1},isEmpty:function t(){var t=!0;this.items.isEmpty()||(t=!1);for(var e=0;4>e;e++)null!==this.subnode[e]&&(this.subnode[e].isEmpty()||(t=!1));return t},add:function(t){this.items.add(t)},interfaces_:function(){return[u]},getClass:function(){return Ni}}),Ni.getSubnodeIndex=function(t,e,n){var i=-1;return t.getMinX()>=e&&(t.getMinY()>=n&&(i=3),t.getMaxY()<=n&&(i=1)),t.getMaxX()<=e&&(t.getMinY()>=n&&(i=2),t.getMaxY()<=n&&(i=0)),i},Ci.exponent=function(t){return Si(64,t)-1023},Ci.powerOf2=function(t){return Math.pow(2,t)},e(wi.prototype,{getLevel:function(){return this.level},computeKey:function(){if(1===arguments.length){var t=arguments[0];for(this.level=wi.computeQuadLevel(t),this.env=new C,this.computeKey(this.level,t);!this.env.contains(t);)this.level+=1,this.computeKey(this.level,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1],i=Ci.powerOf2(e);this.pt.x=Math.floor(n.getMinX()/i)*i,this.pt.y=Math.floor(n.getMinY()/i)*i,this.env.init(this.pt.x,this.pt.x+i,this.pt.y,this.pt.y+i)}},getEnvelope:function(){return this.env},getCentre:function(){return new g((this.env.getMinX()+this.env.getMaxX())/2,(this.env.getMinY()+this.env.getMaxY())/2)},getPoint:function(){return this.pt},interfaces_:function(){return[]},getClass:function(){return wi}}),wi.computeQuadLevel=function(t){var e=t.getWidth(),n=t.getHeight(),i=e>n?e:n,r=Ci.exponent(i)+1;return r},h(Li,Ni),e(Li.prototype,{find:function(t){var e=Ni.getSubnodeIndex(t,this.centrex,this.centrey);if(-1===e)return this;if(null!==this.subnode[e]){var n=this.subnode[e];return n.find(t)}return this},isSearchMatch:function(t){return this.env.intersects(t)},getSubnode:function(t){return null===this.subnode[t]&&(this.subnode[t]=this.createSubnode(t)),this.subnode[t]},getEnvelope:function(){return this.env},getNode:function(t){var e=Ni.getSubnodeIndex(t,this.centrex,this.centrey);if(-1!==e){var n=this.getSubnode(e);return n.getNode(t)}return this},createSubnode:function(t){var e=0,n=0,i=0,r=0;switch(t){case 0:e=this.env.getMinX(),n=this.centrex,i=this.env.getMinY(),r=this.centrey;break;case 1:e=this.centrex,n=this.env.getMaxX(),i=this.env.getMinY(),r=this.centrey;break;case 2:e=this.env.getMinX(),n=this.centrex,i=this.centrey,r=this.env.getMaxY();break;case 3:e=this.centrex,n=this.env.getMaxX(),i=this.centrey,r=this.env.getMaxY()}var s=new C(e,n,i,r),o=new Li(s,this.level-1);return o},insertNode:function(t){f.isTrue(null===this.env||this.env.contains(t.env));var e=Ni.getSubnodeIndex(t.env,this.centrex,this.centrey);if(t.level===this.level-1)this.subnode[e]=t;else{var n=this.createSubnode(e);n.insertNode(t),this.subnode[e]=n}},interfaces_:function(){return[]},getClass:function(){return Li}}),Li.createNode=function(t){var e=new wi(t),n=new Li(e.getEnvelope(),e.getLevel());return n},Li.createExpanded=function(t,e){var n=new C(e);null!==t&&n.expandToInclude(t.env);var i=Li.createNode(n);return null!==t&&i.insertNode(t),i},e(Ri.prototype,{interfaces_:function(){return[]},getClass:function(){return Ri}}),Ri.isZeroWidth=function(t,e){var n=e-t;if(0===n)return!0;var i=Math.max(Math.abs(t),Math.abs(e)),r=n/i,s=Ci.exponent(r);return s<=Ri.MIN_BINARY_EXPONENT},Ri.MIN_BINARY_EXPONENT=-50,h(Ti,Ni),e(Ti.prototype,{insert:function(t,e){var n=Ni.getSubnodeIndex(t,Ti.origin.x,Ti.origin.y);if(-1===n)return this.add(e),null;var i=this.subnode[n];if(null===i||!i.getEnvelope().contains(t)){var r=Li.createExpanded(i,t);this.subnode[n]=r}this.insertContained(this.subnode[n],t,e)},isSearchMatch:function(t){return!0},insertContained:function(t,e,n){f.isTrue(t.getEnvelope().contains(e));var i=Ri.isZeroWidth(e.getMinX(),e.getMaxX()),r=Ri.isZeroWidth(e.getMinY(),e.getMaxY()),s=null;s=i||r?t.find(e):t.getNode(e),s.add(n)},interfaces_:function(){return[]},getClass:function(){return Ti}}),Ti.origin=new g(0,0),e(Pi.prototype,{size:function(){return null!==this.root?this.root.size():0},insert:function(t,e){this.collectStats(t);var n=Pi.ensureExtent(t,this.minExtent);this.root.insert(n,e)},query:function(){if(1===arguments.length){var t=arguments[0],e=new Yn;return this.query(t,e),e.getItems()}if(2===arguments.length){var n=arguments[0],i=arguments[1];this.root.visit(n,i)}},queryAll:function(){var t=new I;return this.root.addAllItems(t),
t},remove:function(t,e){var n=Pi.ensureExtent(t,this.minExtent);return this.root.remove(n,e)},collectStats:function(t){var e=t.getWidth();e<this.minExtent&&e>0&&(this.minExtent=e);var n=t.getHeight();n<this.minExtent&&n>0&&(this.minExtent=n)},depth:function(){return null!==this.root?this.root.depth():0},isEmpty:function(){return null===this.root},interfaces_:function(){return[Fe,u]},getClass:function(){return Pi}}),Pi.ensureExtent=function(t,e){var n=t.getMinX(),i=t.getMaxX(),r=t.getMinY(),s=t.getMaxY();return n!==i&&r!==s?t:(n===i&&(n-=e/2,i=n+e/2),r===s&&(r-=e/2,s=r+e/2),new C(n,i,r,s))},Pi.serialVersionUID=-0x678b60c967a25400;var po=Object.freeze({Quadtree:Pi}),vo=Object.freeze({STRtree:ke}),mo=Object.freeze({quadtree:po,strtree:vo}),yo=["Point","MultiPoint","LineString","MultiLineString","Polygon","MultiPolygon"];e(bi.prototype,{read:function(t){var e=void 0;e="string"==typeof t?JSON.parse(t):t;var n=e.type;if(!xo[n])throw new Error("Unknown GeoJSON type: "+e.type);return-1!==yo.indexOf(n)?xo[n].apply(this,[e.coordinates]):"GeometryCollection"===n?xo[n].apply(this,[e.geometries]):xo[n].apply(this,[e])},write:function(t){var e=t.getGeometryType();if(!Eo[e])throw new Error("Geometry is not supported");return Eo[e].apply(this,[t])}});var xo={Feature:function(t){var e={};for(var n in t)e[n]=t[n];if(t.geometry){var i=t.geometry.type;if(!xo[i])throw new Error("Unknown GeoJSON type: "+t.type);e.geometry=this.read(t.geometry)}return t.bbox&&(e.bbox=xo.bbox.apply(this,[t.bbox])),e},FeatureCollection:function(t){var e={};if(t.features){e.features=[];for(var n=0;n<t.features.length;++n)e.features.push(this.read(t.features[n]))}return t.bbox&&(e.bbox=this.parse.bbox.apply(this,[t.bbox])),e},coordinates:function t(e){for(var t=[],n=0;n<e.length;++n){var i=e[n];t.push(new g(i[0],i[1]))}return t},bbox:function(t){return this.geometryFactory.createLinearRing([new g(t[0],t[1]),new g(t[2],t[1]),new g(t[2],t[3]),new g(t[0],t[3]),new g(t[0],t[1])])},Point:function(t){var e=new g(t[0],t[1]);return this.geometryFactory.createPoint(e)},MultiPoint:function(t){for(var e=[],n=0;n<t.length;++n)e.push(xo.Point.apply(this,[t[n]]));return this.geometryFactory.createMultiPoint(e)},LineString:function(t){var e=xo.coordinates.apply(this,[t]);return this.geometryFactory.createLineString(e)},MultiLineString:function(t){for(var e=[],n=0;n<t.length;++n)e.push(xo.LineString.apply(this,[t[n]]));return this.geometryFactory.createMultiLineString(e)},Polygon:function(t){for(var e=xo.coordinates.apply(this,[t[0]]),n=this.geometryFactory.createLinearRing(e),i=[],r=1;r<t.length;++r){var s=t[r],o=xo.coordinates.apply(this,[s]),a=this.geometryFactory.createLinearRing(o);i.push(a)}return this.geometryFactory.createPolygon(n,i)},MultiPolygon:function(t){for(var e=[],n=0;n<t.length;++n){var i=t[n];e.push(xo.Polygon.apply(this,[i]))}return this.geometryFactory.createMultiPolygon(e)},GeometryCollection:function(t){for(var e=[],n=0;n<t.length;++n){var i=t[n];e.push(this.read(i))}return this.geometryFactory.createGeometryCollection(e)}},Eo={coordinate:function(t){return[t.x,t.y]},Point:function(t){var e=Eo.coordinate.apply(this,[t.getCoordinate()]);return{type:"Point",coordinates:e}},MultiPoint:function(t){for(var e=[],n=0;n<t.geometries.length;++n){var i=t.geometries[n],r=Eo.Point.apply(this,[i]);e.push(r.coordinates)}return{type:"MultiPoint",coordinates:e}},LineString:function(t){for(var e=[],n=t.getCoordinates(),i=0;i<n.length;++i){var r=n[i];e.push(Eo.coordinate.apply(this,[r]))}return{type:"LineString",coordinates:e}},MultiLineString:function(t){for(var e=[],n=0;n<t.geometries.length;++n){var i=t.geometries[n],r=Eo.LineString.apply(this,[i]);e.push(r.coordinates)}return{type:"MultiLineString",coordinates:e}},Polygon:function(t){var e=[],n=Eo.LineString.apply(this,[t.shell]);e.push(n.coordinates);for(var i=0;i<t.holes.length;++i){var r=t.holes[i],s=Eo.LineString.apply(this,[r]);e.push(s.coordinates)}return{type:"Polygon",coordinates:e}},MultiPolygon:function(t){for(var e=[],n=0;n<t.geometries.length;++n){var i=t.geometries[n],r=Eo.Polygon.apply(this,[i]);e.push(r.coordinates)}return{type:"MultiPolygon",coordinates:e}},GeometryCollection:function(t){for(var e=[],n=0;n<t.geometries.length;++n){var i=t.geometries[n],r=i.getGeometryType();e.push(Eo[r].apply(this,[i]))}return{type:"GeometryCollection",geometries:e}}};e(Oi.prototype,{read:function(t){var e=this.parser.read(t);return this.precisionModel.getType()===ee.FIXED&&this.reducePrecision(e),e},reducePrecision:function(t){var e,n;if(t.coordinate)this.precisionModel.makePrecise(t.coordinate);else if(t.points)for(e=0,n=t.points.length;n>e;e++)this.precisionModel.makePrecise(t.points[e]);else if(t.geometries)for(e=0,n=t.geometries.length;n>e;e++)this.reducePrecision(t.geometries[e])}}),e(_i.prototype,{write:function(t){return this.parser.write(t)}}),e(Mi.prototype,{read:function(t){var e=this.parser.read(t);return this.precisionModel.getType()===ee.FIXED&&this.reducePrecision(e),e},reducePrecision:function(t){if(t.coordinate)this.precisionModel.makePrecise(t.coordinate);else if(t.points)for(var e=0,n=t.points.coordinates.length;n>e;e++)this.precisionModel.makePrecise(t.points.coordinates[e]);else if(t.geometries)for(var i=0,r=t.geometries.length;r>i;i++)this.reducePrecision(t.geometries[i])}}),e(Ai.prototype,{read:function(t){return t instanceof ol.geom.Point?this.convertFromPoint(t):t instanceof ol.geom.LineString?this.convertFromLineString(t):t instanceof ol.geom.LinearRing?this.convertFromLinearRing(t):t instanceof ol.geom.Polygon?this.convertFromPolygon(t):t instanceof ol.geom.MultiPoint?this.convertFromMultiPoint(t):t instanceof ol.geom.MultiLineString?this.convertFromMultiLineString(t):t instanceof ol.geom.MultiPolygon?this.convertFromMultiPolygon(t):t instanceof ol.geom.GeometryCollection?this.convertFromCollection(t):void 0},convertFromPoint:function(t){var e=t.getCoordinates();return this.geometryFactory.createPoint(new g(e[0],e[1]))},convertFromLineString:function(t){return this.geometryFactory.createLineString(t.getCoordinates().map(function(t){return new g(t[0],t[1])}))},convertFromLinearRing:function(t){return this.geometryFactory.createLinearRing(t.getCoordinates().map(function(t){return new g(t[0],t[1])}))},convertFromPolygon:function(t){for(var e=t.getLinearRings(),n=null,i=[],r=0;r<e.length;r++){var s=this.convertFromLinearRing(e[r]);0===r?n=s:i.push(s)}return this.geometryFactory.createPolygon(n,i)},convertFromMultiPoint:function(t){var e=t.getPoints().map(function(t){return this.convertFromPoint(t)},this);return this.geometryFactory.createMultiPoint(e)},convertFromMultiLineString:function(t){var e=t.getLineStrings().map(function(t){return this.convertFromLineString(t)},this);return this.geometryFactory.createMultiLineString(e)},convertFromMultiPolygon:function(t){var e=t.getPolygons().map(function(t){return this.convertFromPolygon(t)},this);return this.geometryFactory.createMultiPolygon(e)},convertFromCollection:function(t){var e=t.getGeometries().map(function(t){return this.read(t)},this);return this.geometryFactory.createGeometryCollection(e)},write:function(t){return"Point"===t.getGeometryType()?this.convertToPoint(t.getCoordinate()):"LineString"===t.getGeometryType()?this.convertToLineString(t):"LinearRing"===t.getGeometryType()?this.convertToLinearRing(t):"Polygon"===t.getGeometryType()?this.convertToPolygon(t):"MultiPoint"===t.getGeometryType()?this.convertToMultiPoint(t):"MultiLineString"===t.getGeometryType()?this.convertToMultiLineString(t):"MultiPolygon"===t.getGeometryType()?this.convertToMultiPolygon(t):"GeometryCollection"===t.getGeometryType()?this.convertToCollection(t):void 0},convertToPoint:function(t){return new ol.geom.Point([t.x,t.y])},convertToLineString:function(t){var e=t.points.coordinates.map(Di);return new ol.geom.LineString(e)},convertToLinearRing:function(t){var e=t.points.coordinates.map(Di);return new ol.geom.LinearRing(e)},convertToPolygon:function(t){for(var e=[t.shell.points.coordinates.map(Di)],n=0;n<t.holes.length;n++)e.push(t.holes[n].points.coordinates.map(Di));return new ol.geom.Polygon(e)},convertToMultiPoint:function(t){return new ol.geom.MultiPoint(t.getCoordinates().map(Di))},convertToMultiLineString:function(t){for(var e=[],n=0;n<t.geometries.length;n++)e.push(this.convertToLineString(t.geometries[n]).getCoordinates());return new ol.geom.MultiLineString(e)},convertToMultiPolygon:function(t){for(var e=[],n=0;n<t.geometries.length;n++)e.push(this.convertToPolygon(t.geometries[n]).getCoordinates());return new ol.geom.MultiPolygon(e)},convertToCollection:function(t){for(var e=[],n=0;n<t.geometries.length;n++){var i=t.geometries[n];e.push(this.write(i))}return new ol.geom.GeometryCollection(e)}});var Io=Object.freeze({GeoJSONReader:Oi,GeoJSONWriter:_i,OL3Parser:Ai,WKTReader:Mi,WKTWriter:se});e(Fi.prototype,{rescale:function(){if(R(arguments[0],v))for(var t=arguments[0],e=t.iterator();e.hasNext();){var n=e.next();this.rescale(n.getCoordinates())}else if(arguments[0]instanceof Array){var i=arguments[0],r=null,s=null;2===i.length&&(r=new g(i[0]),s=new g(i[1]));for(var e=0;e<i.length;e++)i[e].x=i[e].x/this.scaleFactor+this.offsetX,i[e].y=i[e].y/this.scaleFactor+this.offsetY;2===i.length&&i[0].equals2D(i[1])&&A.out.println(i)}},scale:function(){if(R(arguments[0],v)){for(var t=arguments[0],e=new I,n=t.iterator();n.hasNext();){var i=n.next();e.add(new Ke(this.scale(i.getCoordinates()),i.getData()))}return e}if(arguments[0]instanceof Array){for(var r=arguments[0],s=new Array(r.length).fill(null),n=0;n<r.length;n++)s[n]=new g(Math.round((r[n].x-this.offsetX)*this.scaleFactor),Math.round((r[n].y-this.offsetY)*this.scaleFactor),r[n].z);var o=H.removeRepeatedPoints(s);return o}},isIntegerPrecision:function(){return 1===this.scaleFactor},getNodedSubstrings:function(){var t=this.noder.getNodedSubstrings();return this.isScaled&&this.rescale(t),t},computeNodes:function(t){var e=t;this.isScaled&&(e=this.scale(t)),this.noder.computeNodes(e)},interfaces_:function(){return[tn]},getClass:function(){return Fi}});var No=Object.freeze({MCIndexNoder:nn,ScaledNoder:Fi,SegmentString:be});e(Gi.prototype,{isSimpleMultiPoint:function(t){if(t.isEmpty())return!0;for(var e=new at,n=0;n<t.getNumGeometries();n++){var i=t.getGeometryN(n),r=i.getCoordinate();if(e.contains(r))return this.nonSimpleLocation=r,!1;e.add(r)}return!0},isSimplePolygonal:function(t){for(var e=kn.getLines(t),n=e.iterator();n.hasNext();){var i=n.next();if(!this.isSimpleLinearGeometry(i))return!1}return!0},hasClosedEndpointIntersection:function(t){for(var e=new rt,n=t.getEdgeIterator();n.hasNext();){var i=n.next(),r=(i.getMaximumSegmentIndex(),i.isClosed()),s=i.getCoordinate(0);this.addEndpoint(e,s,r);var o=i.getCoordinate(i.getNumPoints()-1);this.addEndpoint(e,o,r)}for(var n=e.values().iterator();n.hasNext();){var a=n.next();if(a.isClosed&&2!==a.degree)return this.nonSimpleLocation=a.getCoordinate(),!0}return!1},getNonSimpleLocation:function(){return this.nonSimpleLocation},isSimpleLinearGeometry:function(t){if(t.isEmpty())return!0;var e=new $n(0,t),n=new ae,i=e.computeSelfNodes(n,!0);return i.hasIntersection()?i.hasProperIntersection()?(this.nonSimpleLocation=i.getProperIntersectionPoint(),!1):this.hasNonEndpointIntersection(e)?!1:!this.isClosedEndpointsInInterior||!this.hasClosedEndpointIntersection(e):!0},hasNonEndpointIntersection:function(t){for(var e=t.getEdgeIterator();e.hasNext();)for(var n=e.next(),i=n.getMaximumSegmentIndex(),r=n.getEdgeIntersectionList().iterator();r.hasNext();){var s=r.next();if(!s.isEndPoint(i))return this.nonSimpleLocation=s.getCoordinate(),!0}return!1},addEndpoint:function(t,e,n){var i=t.get(e);null===i&&(i=new qi(e),t.put(e,i)),i.addEndpoint(n)},computeSimple:function(t){return this.nonSimpleLocation=null,t.isEmpty()?!0:t instanceof St?this.isSimpleLinearGeometry(t):t instanceof gt?this.isSimpleLinearGeometry(t):t instanceof Pt?this.isSimpleMultiPoint(t):R(t,Rt)?this.isSimplePolygonal(t):t instanceof ft?this.isSimpleGeometryCollection(t):!0},isSimple:function(){return this.nonSimpleLocation=null,this.computeSimple(this.inputGeom)},isSimpleGeometryCollection:function(t){for(var e=0;e<t.getNumGeometries();e++){var n=t.getGeometryN(e);if(!this.computeSimple(n))return!1}return!0},interfaces_:function(){return[]},getClass:function(){return Gi}}),e(qi.prototype,{addEndpoint:function(t){this.degree++,this.isClosed|=t},getCoordinate:function(){return this.pt},interfaces_:function(){return[]},getClass:function(){return qi}}),Gi.EndpointInfo=qi,e(Bi.prototype,{getEndCapStyle:function(){return this.endCapStyle},isSingleSided:function(){return this._isSingleSided},setQuadrantSegments:function(t){this.quadrantSegments=t,0===this.quadrantSegments&&(this.joinStyle=Bi.JOIN_BEVEL),this.quadrantSegments<0&&(this.joinStyle=Bi.JOIN_MITRE,this.mitreLimit=Math.abs(this.quadrantSegments)),0>=t&&(this.quadrantSegments=1),this.joinStyle!==Bi.JOIN_ROUND&&(this.quadrantSegments=Bi.DEFAULT_QUADRANT_SEGMENTS)},getJoinStyle:function(){return this.joinStyle},setJoinStyle:function(t){this.joinStyle=t},setSimplifyFactor:function(t){this.simplifyFactor=0>t?0:t},getSimplifyFactor:function(){return this.simplifyFactor},getQuadrantSegments:function(){return this.quadrantSegments},setEndCapStyle:function(t){this.endCapStyle=t},getMitreLimit:function(){return this.mitreLimit},setMitreLimit:function(t){this.mitreLimit=t},setSingleSided:function(t){this._isSingleSided=t},interfaces_:function(){return[]},getClass:function(){return Bi}}),Bi.bufferDistanceError=function(t){var e=Math.PI/2/t;return 1-Math.cos(e/2)},Bi.CAP_ROUND=1,Bi.CAP_FLAT=2,Bi.CAP_SQUARE=3,Bi.JOIN_ROUND=1,Bi.JOIN_MITRE=2,Bi.JOIN_BEVEL=3,Bi.DEFAULT_QUADRANT_SEGMENTS=8,Bi.DEFAULT_MITRE_LIMIT=5,Bi.DEFAULT_SIMPLIFY_FACTOR=.01,e(zi.prototype,{getCoordinate:function(){return this.minCoord},getRightmostSide:function(t,e){var n=this.getRightmostSideOfSegment(t,e);return 0>n&&(n=this.getRightmostSideOfSegment(t,e-1)),0>n&&(this.minCoord=null,this.checkForRightmostCoordinate(t)),n},findRightmostEdgeAtVertex:function(){var t=this.minDe.getEdge().getCoordinates();f.isTrue(this.minIndex>0&&this.minIndex<t.length,"rightmost point expected to be interior vertex of edge");var e=t[this.minIndex-1],n=t[this.minIndex+1],i=he.computeOrientation(this.minCoord,n,e),r=!1;e.y<this.minCoord.y&&n.y<this.minCoord.y&&i===he.COUNTERCLOCKWISE?r=!0:e.y>this.minCoord.y&&n.y>this.minCoord.y&&i===he.CLOCKWISE&&(r=!0),r&&(this.minIndex=this.minIndex-1)},getRightmostSideOfSegment:function(t,e){var n=t.getEdge(),i=n.getCoordinates();if(0>e||e+1>=i.length)return-1;if(i[e].y===i[e+1].y)return-1;var r=cn.LEFT;return i[e].y<i[e+1].y&&(r=cn.RIGHT),r},getEdge:function(){return this.orientedDe},checkForRightmostCoordinate:function(t){for(var e=t.getEdge().getCoordinates(),n=0;n<e.length-1;n++)(null===this.minCoord||e[n].x>this.minCoord.x)&&(this.minDe=t,this.minIndex=n,this.minCoord=e[n])},findRightmostEdgeAtNode:function(){var t=this.minDe.getNode(),e=t.getEdges();this.minDe=e.getRightmostEdge(),this.minDe.isForward()||(this.minDe=this.minDe.getSym(),this.minIndex=this.minDe.getEdge().getCoordinates().length-1)},findEdge:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();n.isForward()&&this.checkForRightmostCoordinate(n)}f.isTrue(0!==this.minIndex||this.minCoord.equals(this.minDe.getCoordinate()),"inconsistency in rightmost processing"),0===this.minIndex?this.findRightmostEdgeAtNode():this.findRightmostEdgeAtVertex(),this.orientedDe=this.minDe;var i=this.getRightmostSide(this.minDe,this.minIndex);i===cn.LEFT&&(this.orientedDe=this.minDe.getSym())},interfaces_:function(){return[]},getClass:function(){return zi}}),Vi.prototype.addLast=function(t){this.array_.push(t)},Vi.prototype.removeFirst=function(){return this.array_.shift()},Vi.prototype.isEmpty=function(){return 0===this.array_.length},e(ki.prototype,{clearVisitedEdges:function(){for(var t=this.dirEdgeList.iterator();t.hasNext();){var e=t.next();e.setVisited(!1)}},getRightmostCoordinate:function(){return this.rightMostCoord},computeNodeDepth:function(t){for(var e=null,n=t.getEdges().iterator();n.hasNext();){var i=n.next();if(i.isVisited()||i.getSym().isVisited()){e=i;break}}if(null===e)throw new sn("unable to find edge to compute depths at "+t.getCoordinate());t.getEdges().computeDepths(e);for(var n=t.getEdges().iterator();n.hasNext();){var i=n.next();i.setVisited(!0),this.copySymDepths(i)}},computeDepth:function(t){this.clearVisitedEdges();var e=this.finder.getEdge();e.getNode(),e.getLabel();e.setEdgeDepths(cn.RIGHT,t),this.copySymDepths(e),this.computeDepths(e)},create:function(t){this.addReachable(t),this.finder.findEdge(this.dirEdgeList),this.rightMostCoord=this.finder.getCoordinate()},findResultEdges:function(){for(var t=this.dirEdgeList.iterator();t.hasNext();){var e=t.next();e.getDepth(cn.RIGHT)>=1&&e.getDepth(cn.LEFT)<=0&&!e.isInteriorAreaEdge()&&e.setInResult(!0)}},computeDepths:function(t){var e=new J,n=new Vi,i=t.getNode();for(n.addLast(i),e.add(i),t.setVisited(!0);!n.isEmpty();){var r=n.removeFirst();e.add(r),this.computeNodeDepth(r);for(var s=r.getEdges().iterator();s.hasNext();){var o=s.next(),a=o.getSym();if(!a.isVisited()){var u=a.getNode();e.contains(u)||(n.addLast(u),e.add(u))}}}},compareTo:function(t){var e=t;return this.rightMostCoord.x<e.rightMostCoord.x?-1:this.rightMostCoord.x>e.rightMostCoord.x?1:0},getEnvelope:function(){if(null===this.env){for(var t=new C,e=this.dirEdgeList.iterator();e.hasNext();)for(var n=e.next(),i=n.getEdge().getCoordinates(),r=0;r<i.length-1;r++)t.expandToInclude(i[r]);this.env=t}return this.env},addReachable:function(t){var e=new pe;for(e.add(t);!e.empty();){var n=e.pop();this.add(n,e)}},copySymDepths:function(t){var e=t.getSym();e.setDepth(cn.LEFT,t.getDepth(cn.RIGHT)),e.setDepth(cn.RIGHT,t.getDepth(cn.LEFT))},add:function(t,e){t.setVisited(!0),this.nodes.add(t);for(var n=t.getEdges().iterator();n.hasNext();){var i=n.next();this.dirEdgeList.add(i);var r=i.getSym(),s=r.getNode();s.isVisited()||e.push(s)}},getNodes:function(){return this.nodes},getDirectedEdges:function(){return this.dirEdgeList},interfaces_:function(){return[s]},getClass:function(){return ki}}),e(Yi.prototype,{isDeletable:function(t,e,n,i){var r=this.inputLine[t],s=this.inputLine[e],o=this.inputLine[n];return this.isConcave(r,s,o)&&this.isShallow(r,s,o,i)?this.isShallowSampled(r,s,t,n,i):!1},deleteShallowConcavities:function(){for(var t=1,e=(this.inputLine.length-1,this.findNextNonDeletedIndex(t)),n=this.findNextNonDeletedIndex(e),i=!1;n<this.inputLine.length;){var r=!1;this.isDeletable(t,e,n,this.distanceTol)&&(this.isDeleted[e]=Yi.DELETE,r=!0,i=!0),t=r?n:e,e=this.findNextNonDeletedIndex(t),n=this.findNextNonDeletedIndex(e)}return i},isShallowConcavity:function(t,e,n,i){var r=he.computeOrientation(t,e,n),s=r===this.angleOrientation;if(!s)return!1;var o=he.distancePointLine(e,t,n);return i>o},isShallowSampled:function(t,e,n,i,r){var s=Math.trunc((i-n)/Yi.NUM_PTS_TO_CHECK);0>=s&&(s=1);for(var o=n;i>o;o+=s)if(!this.isShallow(t,e,this.inputLine[o],r))return!1;return!0},isConcave:function t(e,n,i){var r=he.computeOrientation(e,n,i),t=r===this.angleOrientation;return t},simplify:function(t){this.distanceTol=Math.abs(t),0>t&&(this.angleOrientation=he.CLOCKWISE),this.isDeleted=new Array(this.inputLine.length).fill(null);var e=!1;do e=this.deleteShallowConcavities();while(e);return this.collapseLine()},findNextNonDeletedIndex:function(t){for(var e=t+1;e<this.inputLine.length&&this.isDeleted[e]===Yi.DELETE;)e++;return e},isShallow:function(t,e,n,i){var r=he.distancePointLine(e,t,n);return i>r},collapseLine:function(){for(var t=new N,e=0;e<this.inputLine.length;e++)this.isDeleted[e]!==Yi.DELETE&&t.add(this.inputLine[e]);return t.toCoordinateArray()},interfaces_:function(){return[]},getClass:function(){return Yi}}),Yi.simplify=function(t,e){var n=new Yi(t);return n.simplify(e)},Yi.INIT=0,Yi.DELETE=1,Yi.KEEP=1,Yi.NUM_PTS_TO_CHECK=10,e(Ui.prototype,{getCoordinates:function(){var t=this.ptList.toArray(Ui.COORDINATE_ARRAY_TYPE);return t},setPrecisionModel:function(t){this.precisionModel=t},addPt:function(t){var e=new g(t);return this.precisionModel.makePrecise(e),this.isRedundant(e)?null:void this.ptList.add(e)},reverse:function(){},addPts:function(t,e){if(e)for(var n=0;n<t.length;n++)this.addPt(t[n]);else for(var n=t.length-1;n>=0;n--)this.addPt(t[n])},isRedundant:function(t){if(this.ptList.size()<1)return!1;var e=this.ptList.get(this.ptList.size()-1),n=t.distance(e);return n<this.minimimVertexDistance},toString:function(){var t=new ie,e=t.createLineString(this.getCoordinates());return e.toString()},closeRing:function(){if(this.ptList.size()<1)return null;var t=new g(this.ptList.get(0)),e=this.ptList.get(this.ptList.size()-1),n=null;return this.ptList.size()>=2&&(n=this.ptList.get(this.ptList.size()-2)),t.equals(e)?null:void this.ptList.add(t)},setMinimumVertexDistance:function(t){this.minimimVertexDistance=t},interfaces_:function(){return[]},getClass:function(){return Ui}}),Ui.COORDINATE_ARRAY_TYPE=new Array(0).fill(null),e(Xi.prototype,{addNextSegment:function(t,e){if(this.s0=this.s1,this.s1=this.s2,this.s2=t,this.seg0.setCoordinates(this.s0,this.s1),this.computeOffsetSegment(this.seg0,this.side,this.distance,this.offset0),this.seg1.setCoordinates(this.s1,this.s2),this.computeOffsetSegment(this.seg1,this.side,this.distance,this.offset1),this.s1.equals(this.s2))return null;var n=he.computeOrientation(this.s0,this.s1,this.s2),i=n===he.CLOCKWISE&&this.side===cn.LEFT||n===he.COUNTERCLOCKWISE&&this.side===cn.RIGHT;0===n?this.addCollinear(e):i?this.addOutsideTurn(n,e):this.addInsideTurn(n,e)},addLineEndCap:function(t,e){var n=new ce(t,e),i=new ce;this.computeOffsetSegment(n,cn.LEFT,this.distance,i);var r=new ce;this.computeOffsetSegment(n,cn.RIGHT,this.distance,r);var s=e.x-t.x,o=e.y-t.y,a=Math.atan2(o,s);switch(this.bufParams.getEndCapStyle()){case Bi.CAP_ROUND:this.segList.addPt(i.p1),this.addFilletArc(e,a+Math.PI/2,a-Math.PI/2,he.CLOCKWISE,this.distance),this.segList.addPt(r.p1);break;case Bi.CAP_FLAT:this.segList.addPt(i.p1),this.segList.addPt(r.p1);break;case Bi.CAP_SQUARE:var u=new g;u.x=Math.abs(this.distance)*Math.cos(a),u.y=Math.abs(this.distance)*Math.sin(a);var l=new g(i.p1.x+u.x,i.p1.y+u.y),h=new g(r.p1.x+u.x,r.p1.y+u.y);this.segList.addPt(l),this.segList.addPt(h)}},getCoordinates:function(){var t=this.segList.getCoordinates();return t},addMitreJoin:function(t,e,n,i){var r=!0,s=null;try{s=F.intersection(e.p0,e.p1,n.p0,n.p1);var o=0>=i?1:s.distance(t)/Math.abs(i);o>this.bufParams.getMitreLimit()&&(r=!1)}catch(t){if(!(t instanceof w))throw t;s=new g(0,0),r=!1}finally{}r?this.segList.addPt(s):this.addLimitedMitreJoin(e,n,i,this.bufParams.getMitreLimit())},addFilletCorner:function(t,e,n,i,r){var s=e.x-t.x,o=e.y-t.y,a=Math.atan2(o,s),u=n.x-t.x,l=n.y-t.y,h=Math.atan2(l,u);i===he.CLOCKWISE?h>=a&&(a+=2*Math.PI):a>=h&&(a-=2*Math.PI),this.segList.addPt(e),this.addFilletArc(t,a,h,i,r),this.segList.addPt(n)},addOutsideTurn:function(t,e){return this.offset0.p1.distance(this.offset1.p0)<this.distance*Xi.OFFSET_SEGMENT_SEPARATION_FACTOR?(this.segList.addPt(this.offset0.p1),null):void(this.bufParams.getJoinStyle()===Bi.JOIN_MITRE?this.addMitreJoin(this.s1,this.offset0,this.offset1,this.distance):this.bufParams.getJoinStyle()===Bi.JOIN_BEVEL?this.addBevelJoin(this.offset0,this.offset1):(e&&this.segList.addPt(this.offset0.p1),this.addFilletCorner(this.s1,this.offset0.p1,this.offset1.p0,t,this.distance),this.segList.addPt(this.offset1.p0)))},createSquare:function(t){this.segList.addPt(new g(t.x+this.distance,t.y+this.distance)),this.segList.addPt(new g(t.x+this.distance,t.y-this.distance)),this.segList.addPt(new g(t.x-this.distance,t.y-this.distance)),this.segList.addPt(new g(t.x-this.distance,t.y+this.distance)),this.segList.closeRing()},addSegments:function(t,e){this.segList.addPts(t,e)},addFirstSegment:function(){this.segList.addPt(this.offset1.p0)},addLastSegment:function(){this.segList.addPt(this.offset1.p1)},initSideSegments:function(t,e,n){this.s1=t,this.s2=e,this.side=n,this.seg1.setCoordinates(t,e),this.computeOffsetSegment(this.seg1,n,this.distance,this.offset1)},addLimitedMitreJoin:function(t,e,n,i){var r=this.seg0.p1,s=hi.angle(r,this.seg0.p0),o=(hi.angle(r,this.seg1.p1),hi.angleBetweenOriented(this.seg0.p0,r,this.seg1.p1)),a=o/2,u=hi.normalize(s+a),l=hi.normalize(u+Math.PI),h=i*n,c=h*Math.abs(Math.sin(a)),f=n-c,d=r.x+h*Math.cos(l),p=r.y+h*Math.sin(l),v=new g(d,p),m=new ce(r,v),y=m.pointAlongOffset(1,f),x=m.pointAlongOffset(1,-f);this.side===cn.LEFT?(this.segList.addPt(y),this.segList.addPt(x)):(this.segList.addPt(x),this.segList.addPt(y))},computeOffsetSegment:function(t,e,n,i){var r=e===cn.LEFT?1:-1,s=t.p1.x-t.p0.x,o=t.p1.y-t.p0.y,a=Math.sqrt(s*s+o*o),u=r*n*s/a,l=r*n*o/a;i.p0.x=t.p0.x-l,i.p0.y=t.p0.y+u,i.p1.x=t.p1.x-l,i.p1.y=t.p1.y+u},addFilletArc:function(t,e,n,i,r){var s=i===he.CLOCKWISE?-1:1,o=Math.abs(e-n),a=Math.trunc(o/this.filletAngleQuantum+.5);if(1>a)return null;var u=null,l=null;u=0,l=o/a;for(var h=u,c=new g;o>h;){var f=e+s*h;c.x=t.x+r*Math.cos(f),c.y=t.y+r*Math.sin(f),this.segList.addPt(c),h+=l}},addInsideTurn:function(t,e){if(this.li.computeIntersection(this.offset0.p0,this.offset0.p1,this.offset1.p0,this.offset1.p1),this.li.hasIntersection())this.segList.addPt(this.li.getIntersection(0));else if(this._hasNarrowConcaveAngle=!0,this.offset0.p1.distance(this.offset1.p0)<this.distance*Xi.INSIDE_TURN_VERTEX_SNAP_DISTANCE_FACTOR)this.segList.addPt(this.offset0.p1);else{if(this.segList.addPt(this.offset0.p1),this.closingSegLengthFactor>0){var n=new g((this.closingSegLengthFactor*this.offset0.p1.x+this.s1.x)/(this.closingSegLengthFactor+1),(this.closingSegLengthFactor*this.offset0.p1.y+this.s1.y)/(this.closingSegLengthFactor+1));this.segList.addPt(n);var i=new g((this.closingSegLengthFactor*this.offset1.p0.x+this.s1.x)/(this.closingSegLengthFactor+1),(this.closingSegLengthFactor*this.offset1.p0.y+this.s1.y)/(this.closingSegLengthFactor+1));this.segList.addPt(i)}else this.segList.addPt(this.s1);this.segList.addPt(this.offset1.p0)}},createCircle:function(t){var e=new g(t.x+this.distance,t.y);this.segList.addPt(e),this.addFilletArc(t,0,2*Math.PI,-1,this.distance),this.segList.closeRing()},addBevelJoin:function(t,e){this.segList.addPt(t.p1),this.segList.addPt(e.p0)},init:function(t){this.distance=t,this.maxCurveSegmentError=t*(1-Math.cos(this.filletAngleQuantum/2)),this.segList=new Ui,this.segList.setPrecisionModel(this.precisionModel),this.segList.setMinimumVertexDistance(t*Xi.CURVE_VERTEX_SNAP_DISTANCE_FACTOR)},addCollinear:function(t){this.li.computeIntersection(this.s0,this.s1,this.s1,this.s2);var e=this.li.getIntersectionNum();e>=2&&(this.bufParams.getJoinStyle()===Bi.JOIN_BEVEL||this.bufParams.getJoinStyle()===Bi.JOIN_MITRE?(t&&this.segList.addPt(this.offset0.p1),this.segList.addPt(this.offset1.p0)):this.addFilletCorner(this.s1,this.offset0.p1,this.offset1.p0,he.CLOCKWISE,this.distance))},closeRing:function(){this.segList.closeRing()},hasNarrowConcaveAngle:function(){return this._hasNarrowConcaveAngle},interfaces_:function(){return[]},getClass:function(){return Xi}}),Xi.OFFSET_SEGMENT_SEPARATION_FACTOR=.001,Xi.INSIDE_TURN_VERTEX_SNAP_DISTANCE_FACTOR=.001,Xi.CURVE_VERTEX_SNAP_DISTANCE_FACTOR=1e-6,Xi.MAX_CLOSING_SEG_LEN_FACTOR=80,e(Hi.prototype,{getOffsetCurve:function(t,e){if(this.distance=e,0===e)return null;var n=0>e,i=Math.abs(e),r=this.getSegGen(i);t.length<=1?this.computePointCurve(t[0],r):this.computeOffsetCurve(t,n,r);var s=r.getCoordinates();return n&&H.reverse(s),s},computeSingleSidedBufferCurve:function(t,e,n){var i=this.simplifyTolerance(this.distance);if(e){n.addSegments(t,!0);var r=Yi.simplify(t,-i),s=r.length-1;n.initSideSegments(r[s],r[s-1],cn.LEFT),n.addFirstSegment();for(var o=s-2;o>=0;o--)n.addNextSegment(r[o],!0)}else{n.addSegments(t,!1);var a=Yi.simplify(t,i),u=a.length-1;n.initSideSegments(a[0],a[1],cn.LEFT),n.addFirstSegment();for(var o=2;u>=o;o++)n.addNextSegment(a[o],!0)}n.addLastSegment(),n.closeRing()},computeRingBufferCurve:function(t,e,n){var i=this.simplifyTolerance(this.distance);e===cn.RIGHT&&(i=-i);var r=Yi.simplify(t,i),s=r.length-1;n.initSideSegments(r[s-1],r[0],e);for(var o=1;s>=o;o++){var a=1!==o;n.addNextSegment(r[o],a)}n.closeRing()},computeLineBufferCurve:function(t,e){var n=this.simplifyTolerance(this.distance),i=Yi.simplify(t,n),r=i.length-1;e.initSideSegments(i[0],i[1],cn.LEFT);for(var s=2;r>=s;s++)e.addNextSegment(i[s],!0);e.addLastSegment(),e.addLineEndCap(i[r-1],i[r]);var o=Yi.simplify(t,-n),a=o.length-1;e.initSideSegments(o[a],o[a-1],cn.LEFT);for(var s=a-2;s>=0;s--)e.addNextSegment(o[s],!0);e.addLastSegment(),e.addLineEndCap(o[1],o[0]),e.closeRing()},computePointCurve:function(t,e){switch(this.bufParams.getEndCapStyle()){case Bi.CAP_ROUND:e.createCircle(t);break;case Bi.CAP_SQUARE:e.createSquare(t)}},getLineCurve:function(t,e){if(this.distance=e,0>e&&!this.bufParams.isSingleSided())return null;if(0===e)return null;var n=Math.abs(e),i=this.getSegGen(n);if(t.length<=1)this.computePointCurve(t[0],i);else if(this.bufParams.isSingleSided()){var r=0>e;this.computeSingleSidedBufferCurve(t,r,i)}else this.computeLineBufferCurve(t,i);var s=i.getCoordinates();return s},getBufferParameters:function(){return this.bufParams},simplifyTolerance:function(t){return t*this.bufParams.getSimplifyFactor()},getRingCurve:function(t,e,n){if(this.distance=n,t.length<=2)return this.getLineCurve(t,n);if(0===n)return Hi.copyCoordinates(t);var i=this.getSegGen(n);return this.computeRingBufferCurve(t,e,i),i.getCoordinates()},computeOffsetCurve:function(t,e,n){var i=this.simplifyTolerance(this.distance);if(e){var r=Yi.simplify(t,-i),s=r.length-1;n.initSideSegments(r[s],r[s-1],cn.LEFT),n.addFirstSegment();for(var o=s-2;o>=0;o--)n.addNextSegment(r[o],!0)}else{var a=Yi.simplify(t,i),u=a.length-1;n.initSideSegments(a[0],a[1],cn.LEFT),n.addFirstSegment();for(var o=2;u>=o;o++)n.addNextSegment(a[o],!0)}n.addLastSegment()},getSegGen:function(t){return new Xi(this.precisionModel,this.bufParams,t)},interfaces_:function(){return[]},getClass:function(){return Hi}}),Hi.copyCoordinates=function(t){for(var e=new Array(t.length).fill(null),n=0;n<e.length;n++)e[n]=new g(t[n]);return e},e(Wi.prototype,{findStabbedSegments:function(){if(1===arguments.length){for(var t=arguments[0],e=new I,n=this.subgraphs.iterator();n.hasNext();){var i=n.next(),r=i.getEnvelope();t.y<r.getMinY()||t.y>r.getMaxY()||this.findStabbedSegments(t,i.getDirectedEdges(),e)}return e}if(3===arguments.length)if(R(arguments[2],y)&&arguments[0]instanceof g&&arguments[1]instanceof In)for(var s=arguments[0],o=arguments[1],a=arguments[2],u=o.getEdge().getCoordinates(),n=0;n<u.length-1;n++){this.seg.p0=u[n],this.seg.p1=u[n+1],this.seg.p0.y>this.seg.p1.y&&this.seg.reverse();var l=Math.max(this.seg.p0.x,this.seg.p1.x);if(!(l<s.x||this.seg.isHorizontal()||s.y<this.seg.p0.y||s.y>this.seg.p1.y||he.computeOrientation(this.seg.p0,this.seg.p1,s)===he.RIGHT)){var h=o.getDepth(cn.LEFT);this.seg.p0.equals(u[n])||(h=o.getDepth(cn.RIGHT));var c=new ji(this.seg,h);a.add(c)}}else if(R(arguments[2],y)&&arguments[0]instanceof g&&R(arguments[1],y))for(var f=arguments[0],d=arguments[1],p=arguments[2],n=d.iterator();n.hasNext();){var v=n.next();v.isForward()&&this.findStabbedSegments(f,v,p)}},getDepth:function(t){var e=this.findStabbedSegments(t);if(0===e.size())return 0;var n=ho.min(e);return n.leftDepth},interfaces_:function(){return[]},getClass:function(){return Wi}}),e(ji.prototype,{compareTo:function(t){var e=t;if(this.upwardSeg.minX()>=e.upwardSeg.maxX())return 1;if(this.upwardSeg.maxX()<=e.upwardSeg.minX())return-1;var n=this.upwardSeg.orientationIndex(e.upwardSeg);return 0!==n?n:(n=-1*e.upwardSeg.orientationIndex(this.upwardSeg),0!==n?n:this.upwardSeg.compareTo(e.upwardSeg))},compareX:function(t,e){var n=t.p0.compareTo(e.p0);return 0!==n?n:t.p1.compareTo(e.p1)},toString:function(){return this.upwardSeg.toString()},interfaces_:function(){return[s]},getClass:function(){return ji}}),Wi.DepthSegment=ji,e(Ki.prototype,{addPoint:function(t){if(this.distance<=0)return null;
var e=t.getCoordinates(),n=this.curveBuilder.getLineCurve(e,this.distance);this.addCurve(n,L.EXTERIOR,L.INTERIOR)},addPolygon:function(t){var e=this.distance,n=cn.LEFT;this.distance<0&&(e=-this.distance,n=cn.RIGHT);var i=t.getExteriorRing(),r=H.removeRepeatedPoints(i.getCoordinates());if(this.distance<0&&this.isErodedCompletely(i,this.distance))return null;if(this.distance<=0&&r.length<3)return null;this.addPolygonRing(r,e,n,L.EXTERIOR,L.INTERIOR);for(var s=0;s<t.getNumInteriorRing();s++){var o=t.getInteriorRingN(s),a=H.removeRepeatedPoints(o.getCoordinates());this.distance>0&&this.isErodedCompletely(o,-this.distance)||this.addPolygonRing(a,e,cn.opposite(n),L.INTERIOR,L.EXTERIOR)}},isTriangleErodedCompletely:function(t,e){var n=new ci(t[0],t[1],t[2]),i=n.inCentre(),r=he.distancePointLine(i,n.p0,n.p1);return r<Math.abs(e)},addLineString:function(t){if(this.distance<=0&&!this.curveBuilder.getBufferParameters().isSingleSided())return null;var e=H.removeRepeatedPoints(t.getCoordinates()),n=this.curveBuilder.getLineCurve(e,this.distance);this.addCurve(n,L.EXTERIOR,L.INTERIOR)},addCurve:function(t,e,n){if(null===t||t.length<2)return null;var i=new Ke(t,new gn(0,L.BOUNDARY,e,n));this.curveList.add(i)},getCurves:function(){return this.add(this.inputGeom),this.curveList},addPolygonRing:function(t,e,n,i,r){if(0===e&&t.length<bt.MINIMUM_VALID_SIZE)return null;var s=i,o=r;t.length>=bt.MINIMUM_VALID_SIZE&&he.isCCW(t)&&(s=r,o=i,n=cn.opposite(n));var a=this.curveBuilder.getRingCurve(t,n,e);this.addCurve(a,s,o)},add:function(t){if(t.isEmpty())return null;if(t instanceof Tt)this.addPolygon(t);else if(t instanceof St)this.addLineString(t);else if(t instanceof Lt)this.addPoint(t);else if(t instanceof Pt)this.addCollection(t);else if(t instanceof gt)this.addCollection(t);else if(t instanceof Ot)this.addCollection(t);else{if(!(t instanceof ft))throw new UnsupportedOperationException(t.getClass().getName());this.addCollection(t)}},isErodedCompletely:function(t,e){var n=t.getCoordinates();if(n.length<4)return 0>e;if(4===n.length)return this.isTriangleErodedCompletely(n,e);var i=t.getEnvelopeInternal(),r=Math.min(i.getHeight(),i.getWidth());return 0>e&&2*Math.abs(e)>r},addCollection:function(t){for(var e=0;e<t.getNumGeometries();e++){var n=t.getGeometryN(e);this.add(n)}},interfaces_:function(){return[]},getClass:function(){return Ki}}),e(Zi.prototype,{isTrivialIntersection:function(t,e,n,i){if(t===n&&1===this.li.getIntersectionNum()){if(Zi.isAdjacentSegments(e,i))return!0;if(t.isClosed()){var r=t.size()-1;if(0===e&&i===r||0===i&&e===r)return!0}}return!1},getProperIntersectionPoint:function(){return this.properIntersectionPoint},hasProperInteriorIntersection:function(){return this.hasProperInterior},getLineIntersector:function(){return this.li},hasProperIntersection:function(){return this.hasProper},processIntersections:function(t,e,n,i){if(t===n&&e===i)return null;this.numTests++;var r=t.getCoordinates()[e],s=t.getCoordinates()[e+1],o=n.getCoordinates()[i],a=n.getCoordinates()[i+1];this.li.computeIntersection(r,s,o,a),this.li.hasIntersection()&&(this.numIntersections++,this.li.isInteriorIntersection()&&(this.numInteriorIntersections++,this.hasInterior=!0),this.isTrivialIntersection(t,e,n,i)||(this._hasIntersection=!0,t.addIntersections(this.li,e,0),n.addIntersections(this.li,i,1),this.li.isProper()&&(this.numProperIntersections++,this.hasProper=!0,this.hasProperInterior=!0)))},hasIntersection:function(){return this._hasIntersection},isDone:function(){return!1},hasInteriorIntersection:function(){return this.hasInterior},interfaces_:function(){return[on]},getClass:function(){return Zi}}),Zi.isAdjacentSegments=function(t,e){return 1===Math.abs(t-e)},e(Qi.prototype,{setWorkingPrecisionModel:function(t){this.workingPrecisionModel=t},insertUniqueEdge:function(t){var e=this.edgeList.findEqualEdge(t);if(null!==e){var n=e.getLabel(),i=t.getLabel();e.isPointwiseEqual(t)||(i=new gn(t.getLabel()),i.flip()),n.merge(i);var r=Qi.depthDelta(i),s=e.getDepthDelta(),o=s+r;e.setDepthDelta(o)}else this.edgeList.add(t),t.setDepthDelta(Qi.depthDelta(t.getLabel()))},buildSubgraphs:function(t,e){for(var n=new I,i=t.iterator();i.hasNext();){var r=i.next(),s=r.getRightmostCoordinate(),o=new Wi(n),a=o.getDepth(s);r.computeDepth(a),r.findResultEdges(),n.add(r),e.add(r.getDirectedEdges(),r.getNodes())}},createSubgraphs:function(t){for(var e=new I,n=t.getNodes().iterator();n.hasNext();){var i=n.next();if(!i.isVisited()){var r=new ki;r.create(i),e.add(r)}}return ho.sort(e,ho.reverseOrder()),e},createEmptyResultGeometry:function(){var t=this.geomFact.createPolygon();return t},getNoder:function(t){if(null!==this.workingNoder)return this.workingNoder;var e=new nn,n=new ae;return n.setPrecisionModel(t),e.setSegmentIntersector(new Zi(n)),e},buffer:function(t,e){var n=this.workingPrecisionModel;null===n&&(n=t.getPrecisionModel()),this.geomFact=t.getFactory();var i=new Hi(n,this.bufParams),r=new Ki(t,e,i),s=r.getCurves();if(s.size()<=0)return this.createEmptyResultGeometry();this.computeNodedEdges(s,n),this.graph=new Cn(new On),this.graph.addEdges(this.edgeList.getEdges());var o=this.createSubgraphs(this.graph),a=new Sn(this.geomFact);this.buildSubgraphs(o,a);var u=a.getPolygons();if(u.size()<=0)return this.createEmptyResultGeometry();var l=this.geomFact.buildGeometry(u);return l},computeNodedEdges:function(t,e){var n=this.getNoder(e);n.computeNodes(t);for(var i=n.getNodedSubstrings(),r=i.iterator();r.hasNext();){var s=r.next(),o=s.getCoordinates();if(2!==o.length||!o[0].equals2D(o[1])){var a=s.getData(),u=new Jn(s.getCoordinates(),new gn(a));this.insertUniqueEdge(u)}}},setNoder:function(t){this.workingNoder=t},interfaces_:function(){return[]},getClass:function(){return Qi}}),Qi.depthDelta=function(t){var e=t.getLocation(0,cn.LEFT),n=t.getLocation(0,cn.RIGHT);return e===L.INTERIOR&&n===L.EXTERIOR?1:e===L.EXTERIOR&&n===L.INTERIOR?-1:0},Qi.convertSegStrings=function(t){for(var e=new ie,n=new I;t.hasNext();){var i=t.next(),r=e.createLineString(i.getCoordinates());n.add(r)}return e.buildGeometry(n)},e(Ji.prototype,{checkEndPtVertexIntersections:function(){if(0===arguments.length)for(var t=this.segStrings.iterator();t.hasNext();){var e=t.next(),n=e.getCoordinates();this.checkEndPtVertexIntersections(n[0],this.segStrings),this.checkEndPtVertexIntersections(n[n.length-1],this.segStrings)}else if(2===arguments.length)for(var i=arguments[0],r=arguments[1],t=r.iterator();t.hasNext();)for(var e=t.next(),n=e.getCoordinates(),s=1;s<n.length-1;s++)if(n[s].equals(i))throw new l("found endpt/interior pt intersection at index "+s+" :pt "+i)},checkInteriorIntersections:function(){if(0===arguments.length)for(var t=this.segStrings.iterator();t.hasNext();)for(var e=t.next(),n=this.segStrings.iterator();n.hasNext();){var i=n.next();this.checkInteriorIntersections(e,i)}else if(2===arguments.length)for(var r=arguments[0],s=arguments[1],o=r.getCoordinates(),a=s.getCoordinates(),u=0;u<o.length-1;u++)for(var h=0;h<a.length-1;h++)this.checkInteriorIntersections(r,u,s,h);else if(4===arguments.length){var c=arguments[0],f=arguments[1],g=arguments[2],d=arguments[3];if(c===g&&f===d)return null;var p=c.getCoordinates()[f],v=c.getCoordinates()[f+1],m=g.getCoordinates()[d],y=g.getCoordinates()[d+1];if(this.li.computeIntersection(p,v,m,y),this.li.hasIntersection()&&(this.li.isProper()||this.hasInteriorIntersection(this.li,p,v)||this.hasInteriorIntersection(this.li,m,y)))throw new l("found non-noded intersection at "+p+"-"+v+" and "+m+"-"+y)}},checkValid:function(){this.checkEndPtVertexIntersections(),this.checkInteriorIntersections(),this.checkCollapses()},checkCollapses:function(){if(0===arguments.length)for(var t=this.segStrings.iterator();t.hasNext();){var e=t.next();this.checkCollapses(e)}else if(1===arguments.length)for(var n=arguments[0],i=n.getCoordinates(),t=0;t<i.length-2;t++)this.checkCollapse(i[t],i[t+1],i[t+2])},hasInteriorIntersection:function(t,e,n){for(var i=0;i<t.getIntersectionNum();i++){var r=t.getIntersection(i);if(!r.equals(e)&&!r.equals(n))return!0}return!1},checkCollapse:function(t,e,n){if(t.equals(n))throw new l("found non-noded collapse at "+Ji.fact.createLineString([t,e,n]))},interfaces_:function(){return[]},getClass:function(){return Ji}}),Ji.fact=new ie,e($i.prototype,{intersectsScaled:function(t,e){var n=Math.min(t.x,e.x),i=Math.max(t.x,e.x),r=Math.min(t.y,e.y),s=Math.max(t.y,e.y),o=this.maxx<n||this.minx>i||this.maxy<r||this.miny>s;if(o)return!1;var a=this.intersectsToleranceSquare(t,e);return f.isTrue(!(o&&a),"Found bad envelope test"),a},initCorners:function(t){var e=.5;this.minx=t.x-e,this.maxx=t.x+e,this.miny=t.y-e,this.maxy=t.y+e,this.corner[0]=new g(this.maxx,this.maxy),this.corner[1]=new g(this.minx,this.maxy),this.corner[2]=new g(this.minx,this.miny),this.corner[3]=new g(this.maxx,this.miny)},intersects:function(t,e){return 1===this.scaleFactor?this.intersectsScaled(t,e):(this.copyScaled(t,this.p0Scaled),this.copyScaled(e,this.p1Scaled),this.intersectsScaled(this.p0Scaled,this.p1Scaled))},scale:function(t){return Math.round(t*this.scaleFactor)},getCoordinate:function(){return this.originalPt},copyScaled:function(t,e){e.x=this.scale(t.x),e.y=this.scale(t.y)},getSafeEnvelope:function(){if(null===this.safeEnv){var t=$i.SAFE_ENV_EXPANSION_FACTOR/this.scaleFactor;this.safeEnv=new C(this.originalPt.x-t,this.originalPt.x+t,this.originalPt.y-t,this.originalPt.y+t)}return this.safeEnv},intersectsPixelClosure:function(t,e){return this.li.computeIntersection(t,e,this.corner[0],this.corner[1]),this.li.hasIntersection()?!0:(this.li.computeIntersection(t,e,this.corner[1],this.corner[2]),this.li.hasIntersection()?!0:(this.li.computeIntersection(t,e,this.corner[2],this.corner[3]),this.li.hasIntersection()?!0:(this.li.computeIntersection(t,e,this.corner[3],this.corner[0]),!!this.li.hasIntersection())))},intersectsToleranceSquare:function(t,e){var n=!1,i=!1;return this.li.computeIntersection(t,e,this.corner[0],this.corner[1]),this.li.isProper()?!0:(this.li.computeIntersection(t,e,this.corner[1],this.corner[2]),this.li.isProper()?!0:(this.li.hasIntersection()&&(n=!0),this.li.computeIntersection(t,e,this.corner[2],this.corner[3]),this.li.isProper()?!0:(this.li.hasIntersection()&&(i=!0),this.li.computeIntersection(t,e,this.corner[3],this.corner[0]),this.li.isProper()?!0:n&&i?!0:t.equals(this.pt)?!0:!!e.equals(this.pt))))},addSnappedNode:function(t,e){var n=t.getCoordinate(e),i=t.getCoordinate(e+1);return this.intersects(n,i)?(t.addIntersection(this.getCoordinate(),e),!0):!1},interfaces_:function(){return[]},getClass:function(){return $i}}),$i.SAFE_ENV_EXPANSION_FACTOR=.75,e(tr.prototype,{select:function(){if(1===arguments.length){arguments[0]}else if(2===arguments.length){var t=arguments[0],e=arguments[1];t.getLineSegment(e,this.selectedSegment),this.select(this.selectedSegment)}},interfaces_:function(){return[]},getClass:function(){return tr}}),e(er.prototype,{snap:function(){if(1===arguments.length){var t=arguments[0];return this.snap(t,null,-1)}if(3===arguments.length){var e=arguments[0],n=arguments[1],i=arguments[2],r=e.getSafeEnvelope(),s=new nr(e,n,i);return this.index.query(r,{interfaces_:function(){return[Ae]},visitItem:function(t){var e=t;e.select(r,s)}}),s.isNodeAdded()}},interfaces_:function(){return[]},getClass:function(){return er}}),h(nr,tr),e(nr.prototype,{isNodeAdded:function(){return this._isNodeAdded},select:function(){if(2!==arguments.length)return tr.prototype.select.apply(this,arguments);var t=arguments[0],e=arguments[1],n=t.getContext();return null!==this.parentEdge&&n===this.parentEdge&&e===this.hotPixelVertexIndex?null:void(this._isNodeAdded=this.hotPixel.addSnappedNode(n,e))},interfaces_:function(){return[]},getClass:function(){return nr}}),er.HotPixelSnapAction=nr,e(ir.prototype,{processIntersections:function(t,e,n,i){if(t===n&&e===i)return null;var r=t.getCoordinates()[e],s=t.getCoordinates()[e+1],o=n.getCoordinates()[i],a=n.getCoordinates()[i+1];if(this.li.computeIntersection(r,s,o,a),this.li.hasIntersection()&&this.li.isInteriorIntersection()){for(var u=0;u<this.li.getIntersectionNum();u++)this.interiorIntersections.add(this.li.getIntersection(u));t.addIntersections(this.li,e,0),n.addIntersections(this.li,i,1)}},isDone:function(){return!1},getInteriorIntersections:function(){return this.interiorIntersections},interfaces_:function(){return[on]},getClass:function(){return ir}}),e(rr.prototype,{checkCorrectness:function(t){var e=Ke.getNodedSubstrings(t),n=new Ji(e);try{n.checkValid()}catch(t){if(!(t instanceof S))throw t;t.printStackTrace()}finally{}},getNodedSubstrings:function(){return Ke.getNodedSubstrings(this.nodedSegStrings)},snapRound:function(t,e){var n=this.findInteriorIntersections(t,e);this.computeIntersectionSnaps(n),this.computeVertexSnaps(t)},findInteriorIntersections:function(t,e){var n=new ir(e);return this.noder.setSegmentIntersector(n),this.noder.computeNodes(t),n.getInteriorIntersections()},computeVertexSnaps:function(){if(R(arguments[0],v))for(var t=arguments[0],e=t.iterator();e.hasNext();){var n=e.next();this.computeVertexSnaps(n)}else if(arguments[0]instanceof Ke)for(var i=arguments[0],r=i.getCoordinates(),s=0;s<r.length;s++){var o=new $i(r[s],this.scaleFactor,this.li),a=this.pointSnapper.snap(o,i,s);a&&i.addIntersection(r[s],s)}},computeNodes:function(t){this.nodedSegStrings=t,this.noder=new nn,this.pointSnapper=new er(this.noder.getIndex()),this.snapRound(t,this.li)},computeIntersectionSnaps:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next(),i=new $i(n,this.scaleFactor,this.li);this.pointSnapper.snap(i)}},interfaces_:function(){return[tn]},getClass:function(){return rr}}),e(sr.prototype,{bufferFixedPrecision:function(t){var e=new Fi(new rr(new ee(1)),t.getScale()),n=new Qi(this.bufParams);n.setWorkingPrecisionModel(t),n.setNoder(e),this.resultGeometry=n.buffer(this.argGeom,this.distance)},bufferReducedPrecision:function(){if(0===arguments.length){for(var t=sr.MAX_PRECISION_DIGITS;t>=0;t--){try{this.bufferReducedPrecision(t)}catch(t){if(!(t instanceof sn))throw t;this.saveException=t}finally{}if(null!==this.resultGeometry)return null}throw this.saveException}if(1===arguments.length){var e=arguments[0],n=sr.precisionScaleFactor(this.argGeom,this.distance,e),i=new ee(n);this.bufferFixedPrecision(i)}},computeGeometry:function(){if(this.bufferOriginalPrecision(),null!==this.resultGeometry)return null;var t=this.argGeom.getFactory().getPrecisionModel();t.getType()===ee.FIXED?this.bufferFixedPrecision(t):this.bufferReducedPrecision()},setQuadrantSegments:function(t){this.bufParams.setQuadrantSegments(t)},bufferOriginalPrecision:function(){try{var t=new Qi(this.bufParams);this.resultGeometry=t.buffer(this.argGeom,this.distance)}catch(t){if(!(t instanceof l))throw t;this.saveException=t}finally{}},getResultGeometry:function(t){return this.distance=t,this.computeGeometry(),this.resultGeometry},setEndCapStyle:function(t){this.bufParams.setEndCapStyle(t)},interfaces_:function(){return[]},getClass:function(){return sr}}),sr.bufferOp=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=new sr(t),i=n.getResultGeometry(e);return i}if(3===arguments.length){if(Number.isInteger(arguments[2])&&arguments[0]instanceof B&&"number"==typeof arguments[1]){var r=arguments[0],s=arguments[1],o=arguments[2],a=new sr(r);a.setQuadrantSegments(o);var i=a.getResultGeometry(s);return i}if(arguments[2]instanceof Bi&&arguments[0]instanceof B&&"number"==typeof arguments[1]){var u=arguments[0],l=arguments[1],h=arguments[2],a=new sr(u,h),i=a.getResultGeometry(l);return i}}else if(4===arguments.length){var c=arguments[0],f=arguments[1],g=arguments[2],d=arguments[3],a=new sr(c);a.setQuadrantSegments(g),a.setEndCapStyle(d);var i=a.getResultGeometry(f);return i}},sr.precisionScaleFactor=function(t,e,n){var i=t.getEnvelopeInternal(),r=T.max(Math.abs(i.getMaxX()),Math.abs(i.getMaxY()),Math.abs(i.getMinX()),Math.abs(i.getMinY())),s=e>0?e:0,o=r+2*s,a=Math.trunc(Math.log(o)/Math.log(10)+1),u=n-a,l=Math.pow(10,u);return l},sr.CAP_ROUND=Bi.CAP_ROUND,sr.CAP_BUTT=Bi.CAP_FLAT,sr.CAP_FLAT=Bi.CAP_FLAT,sr.CAP_SQUARE=Bi.CAP_SQUARE,sr.MAX_PRECISION_DIGITS=12;var Co=Object.freeze({BufferOp:sr,BufferParameters:Bi});e(or.prototype,{filter:function(t){t instanceof Tt&&this.comps.add(t)},interfaces_:function(){return[ht]},getClass:function(){return or}}),or.getPolygons=function(){if(1===arguments.length){var t=arguments[0];return or.getPolygons(t,new I)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return e instanceof Tt?n.add(e):e instanceof ft&&e.apply(new or(n)),n}},e(ar.prototype,{isInsideArea:function(){return this.segIndex===ar.INSIDE_AREA},getCoordinate:function(){return this.pt},getGeometryComponent:function(){return this.component},getSegmentIndex:function(){return this.segIndex},interfaces_:function(){return[]},getClass:function(){return ar}}),ar.INSIDE_AREA=-1,e(ur.prototype,{filter:function(t){t instanceof Lt&&this.pts.add(t)},interfaces_:function(){return[ht]},getClass:function(){return ur}}),ur.getPoints=function(){if(1===arguments.length){var t=arguments[0];return t instanceof Lt?ho.singletonList(t):ur.getPoints(t,new I)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return e instanceof Lt?n.add(e):e instanceof ft&&e.apply(new ur(n)),n}},e(lr.prototype,{filter:function(t){(t instanceof Lt||t instanceof St||t instanceof Tt)&&this.locations.add(new ar(t,0,t.getCoordinate()))},interfaces_:function(){return[ht]},getClass:function(){return lr}}),lr.getLocations=function(t){var e=new I;return t.apply(new lr(e)),e},e(hr.prototype,{computeContainmentDistance:function(){if(0===arguments.length){var t=new Array(2).fill(null);if(this.computeContainmentDistance(0,t),this.minDistance<=this.terminateDistance)return null;this.computeContainmentDistance(1,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1],i=1-e,r=or.getPolygons(this.geom[e]);if(r.size()>0){var s=lr.getLocations(this.geom[i]);if(this.computeContainmentDistance(s,r,n),this.minDistance<=this.terminateDistance)return this.minDistanceLocation[i]=n[0],this.minDistanceLocation[e]=n[1],null}}else if(3===arguments.length)if(arguments[2]instanceof Array&&R(arguments[0],y)&&R(arguments[1],y)){for(var o=arguments[0],a=arguments[1],u=arguments[2],l=0;l<o.size();l++)for(var h=o.get(l),c=0;c<a.size();c++)if(this.computeContainmentDistance(h,a.get(c),u),this.minDistance<=this.terminateDistance)return null}else if(arguments[2]instanceof Array&&arguments[0]instanceof ar&&arguments[1]instanceof Tt){var f=arguments[0],g=arguments[1],d=arguments[2],p=f.getCoordinate();if(L.EXTERIOR!==this.ptLocator.locate(p,g))return this.minDistance=0,d[0]=f,d[1]=new ar(g,p),null}},computeMinDistanceLinesPoints:function(t,e,n){for(var i=0;i<t.size();i++)for(var r=t.get(i),s=0;s<e.size();s++){var o=e.get(s);if(this.computeMinDistance(r,o,n),this.minDistance<=this.terminateDistance)return null}},computeFacetDistance:function(){var t=new Array(2).fill(null),e=kn.getLines(this.geom[0]),n=kn.getLines(this.geom[1]),i=ur.getPoints(this.geom[0]),r=ur.getPoints(this.geom[1]);return this.computeMinDistanceLines(e,n,t),this.updateMinDistance(t,!1),this.minDistance<=this.terminateDistance?null:(t[0]=null,t[1]=null,this.computeMinDistanceLinesPoints(e,r,t),this.updateMinDistance(t,!1),this.minDistance<=this.terminateDistance?null:(t[0]=null,t[1]=null,this.computeMinDistanceLinesPoints(n,i,t),this.updateMinDistance(t,!0),this.minDistance<=this.terminateDistance?null:(t[0]=null,t[1]=null,this.computeMinDistancePoints(i,r,t),void this.updateMinDistance(t,!1))))},nearestLocations:function(){return this.computeMinDistance(),this.minDistanceLocation},updateMinDistance:function(t,e){return null===t[0]?null:void(e?(this.minDistanceLocation[0]=t[1],this.minDistanceLocation[1]=t[0]):(this.minDistanceLocation[0]=t[0],this.minDistanceLocation[1]=t[1]))},nearestPoints:function(){this.computeMinDistance();var t=[this.minDistanceLocation[0].getCoordinate(),this.minDistanceLocation[1].getCoordinate()];return t},computeMinDistance:function(){if(0===arguments.length){if(null!==this.minDistanceLocation)return null;if(this.minDistanceLocation=new Array(2).fill(null),this.computeContainmentDistance(),this.minDistance<=this.terminateDistance)return null;this.computeFacetDistance()}else if(3===arguments.length)if(arguments[2]instanceof Array&&arguments[0]instanceof St&&arguments[1]instanceof Lt){var t=arguments[0],e=arguments[1],n=arguments[2];if(t.getEnvelopeInternal().distance(e.getEnvelopeInternal())>this.minDistance)return null;for(var i=t.getCoordinates(),r=e.getCoordinate(),s=0;s<i.length-1;s++){var o=he.distancePointLine(r,i[s],i[s+1]);if(o<this.minDistance){this.minDistance=o;var a=new ce(i[s],i[s+1]),u=a.closestPoint(r);n[0]=new ar(t,s,u),n[1]=new ar(e,0,r)}if(this.minDistance<=this.terminateDistance)return null}}else if(arguments[2]instanceof Array&&arguments[0]instanceof St&&arguments[1]instanceof St){var l=arguments[0],h=arguments[1],c=arguments[2];if(l.getEnvelopeInternal().distance(h.getEnvelopeInternal())>this.minDistance)return null;for(var i=l.getCoordinates(),f=h.getCoordinates(),s=0;s<i.length-1;s++)for(var g=0;g<f.length-1;g++){var o=he.distanceLineLine(i[s],i[s+1],f[g],f[g+1]);if(o<this.minDistance){this.minDistance=o;var d=new ce(i[s],i[s+1]),p=new ce(f[g],f[g+1]),v=d.closestPoints(p);c[0]=new ar(l,s,v[0]),c[1]=new ar(h,g,v[1])}if(this.minDistance<=this.terminateDistance)return null}}},computeMinDistancePoints:function(t,e,n){for(var i=0;i<t.size();i++)for(var r=t.get(i),s=0;s<e.size();s++){var o=e.get(s),a=r.getCoordinate().distance(o.getCoordinate());if(a<this.minDistance&&(this.minDistance=a,n[0]=new ar(r,0,r.getCoordinate()),n[1]=new ar(o,0,o.getCoordinate())),this.minDistance<=this.terminateDistance)return null}},distance:function(){if(null===this.geom[0]||null===this.geom[1])throw new i("null geometries are not supported");return this.geom[0].isEmpty()||this.geom[1].isEmpty()?0:(this.computeMinDistance(),this.minDistance)},computeMinDistanceLines:function(t,e,n){for(var i=0;i<t.size();i++)for(var r=t.get(i),s=0;s<e.size();s++){var o=e.get(s);if(this.computeMinDistance(r,o,n),this.minDistance<=this.terminateDistance)return null}},interfaces_:function(){return[]},getClass:function(){return hr}}),hr.distance=function(t,e){var n=new hr(t,e);return n.distance()},hr.isWithinDistance=function(t,e,n){var i=new hr(t,e,n);return i.distance()<=n},hr.nearestPoints=function(t,e){var n=new hr(t,e);return n.nearestPoints()};var So=Object.freeze({DistanceOp:hr});e(cr.prototype,{getCoordinates:function(){if(null===this.coordinates){for(var t=0,e=0,n=new N,i=this.directedEdges.iterator();i.hasNext();){var r=i.next();r.getEdgeDirection()?t++:e++,n.add(r.getEdge().getLine().getCoordinates(),!1,r.getEdgeDirection())}this.coordinates=n.toCoordinateArray(),e>t&&H.reverse(this.coordinates)}return this.coordinates},toLineString:function(){return this.factory.createLineString(this.getCoordinates())},add:function(t){this.directedEdges.add(t)},interfaces_:function(){return[]},getClass:function(){return cr}}),e(fr.prototype,{setVisited:function(t){this._isVisited=t},isMarked:function(){return this._isMarked},setData:function(t){this.data=t},getData:function(){return this.data},setMarked:function(t){this._isMarked=t},getContext:function(){return this.data},isVisited:function(){return this._isVisited},setContext:function(t){this.data=t},interfaces_:function(){return[]},getClass:function(){return fr}}),fr.getComponentWithVisitedState=function(t,e){for(;t.hasNext();){var n=t.next();if(n.isVisited()===e)return n}return null},fr.setVisited=function(t,e){for(;t.hasNext();){var n=t.next();n.setVisited(e)}},fr.setMarked=function(t,e){for(;t.hasNext();){var n=t.next();n.setMarked(e)}},h(gr,fr),e(gr.prototype,{isRemoved:function(){return null===this.parentEdge},compareDirection:function(t){return this.quadrant>t.quadrant?1:this.quadrant<t.quadrant?-1:he.computeOrientation(t.p0,t.p1,this.p1)},getCoordinate:function(){return this.from.getCoordinate()},print:function(t){var e=this.getClass().getName(),n=e.lastIndexOf("."),i=e.substring(n+1);t.print("  "+i+": "+this.p0+" - "+this.p1+" "+this.quadrant+":"+this.angle)},getDirectionPt:function(){return this.p1},getAngle:function(){return this.angle},compareTo:function(t){var e=t;return this.compareDirection(e)},getFromNode:function(){return this.from},getSym:function(){return this.sym},setEdge:function(t){this.parentEdge=t},remove:function(){this.sym=null,this.parentEdge=null},getEdge:function(){return this.parentEdge},getQuadrant:function(){return this.quadrant},setSym:function(t){this.sym=t},getToNode:function(){return this.to},getEdgeDirection:function(){return this.edgeDirection},interfaces_:function(){return[s]},getClass:function(){return gr}}),gr.toEdges=function(t){for(var e=new I,n=t.iterator();n.hasNext();)e.add(n.next().parentEdge);return e},h(dr,gr),e(dr.prototype,{getNext:function(){return 2!==this.getToNode().getDegree()?null:this.getToNode().getOutEdges().getEdges().get(0)===this.getSym()?this.getToNode().getOutEdges().getEdges().get(1):(f.isTrue(this.getToNode().getOutEdges().getEdges().get(1)===this.getSym()),this.getToNode().getOutEdges().getEdges().get(0))},interfaces_:function(){return[]},getClass:function(){return dr}}),h(pr,fr),e(pr.prototype,{isRemoved:function(){return null===this.dirEdge},setDirectedEdges:function(t,e){this.dirEdge=[t,e],t.setEdge(this),e.setEdge(this),t.setSym(e),e.setSym(t),t.getFromNode().addOutEdge(t),e.getFromNode().addOutEdge(e)},getDirEdge:function(){if(Number.isInteger(arguments[0])){var t=arguments[0];return this.dirEdge[t]}if(arguments[0]instanceof mr){var e=arguments[0];return this.dirEdge[0].getFromNode()===e?this.dirEdge[0]:this.dirEdge[1].getFromNode()===e?this.dirEdge[1]:null}},remove:function(){this.dirEdge=null},getOppositeNode:function(t){return this.dirEdge[0].getFromNode()===t?this.dirEdge[0].getToNode():this.dirEdge[1].getFromNode()===t?this.dirEdge[1].getToNode():null},interfaces_:function(){return[]},getClass:function(){return pr}}),e(vr.prototype,{getNextEdge:function(t){var e=this.getIndex(t);return this.outEdges.get(this.getIndex(e+1))},getCoordinate:function(){var t=this.iterator();if(!t.hasNext())return null;var e=t.next();return e.getCoordinate()},iterator:function(){return this.sortEdges(),this.outEdges.iterator()},sortEdges:function(){this.sorted||(ho.sort(this.outEdges),this.sorted=!0)},remove:function(t){this.outEdges.remove(t)},getEdges:function(){return this.sortEdges(),this.outEdges},getNextCWEdge:function(t){var e=this.getIndex(t);return this.outEdges.get(this.getIndex(e-1))},getIndex:function(){if(arguments[0]instanceof pr){var t=arguments[0];this.sortEdges();for(var e=0;e<this.outEdges.size();e++){var n=this.outEdges.get(e);if(n.getEdge()===t)return e}return-1}if(arguments[0]instanceof gr){var i=arguments[0];this.sortEdges();for(var e=0;e<this.outEdges.size();e++){var n=this.outEdges.get(e);if(n===i)return e}return-1}if(Number.isInteger(arguments[0])){var r=arguments[0],s=r%this.outEdges.size();return 0>s&&(s+=this.outEdges.size()),s}},add:function(t){this.outEdges.add(t),this.sorted=!1},getDegree:function(){return this.outEdges.size()},interfaces_:function(){return[]},getClass:function(){return vr}}),h(mr,fr),e(mr.prototype,{isRemoved:function(){return null===this.pt},addOutEdge:function(t){this.deStar.add(t)},getCoordinate:function(){return this.pt},getOutEdges:function(){return this.deStar},remove:function(){if(0===arguments.length)this.pt=null;else if(1===arguments.length){var t=arguments[0];this.deStar.remove(t)}},getIndex:function(t){return this.deStar.getIndex(t)},getDegree:function(){return this.deStar.getDegree()},interfaces_:function(){return[]},getClass:function(){return mr}}),mr.getEdgesBetween=function(t,e){var n=gr.toEdges(t.getOutEdges().getEdges()),i=new J(n),r=gr.toEdges(e.getOutEdges().getEdges());return i.retainAll(r),i},h(yr,pr),e(yr.prototype,{getLine:function(){return this.line},interfaces_:function(){return[]},getClass:function(){return yr}}),e(xr.prototype,{find:function(t){return this.nodeMap.get(t)},iterator:function(){return this.nodeMap.values().iterator()},remove:function(t){return this.nodeMap.remove(t)},values:function(){return this.nodeMap.values()},add:function(t){return this.nodeMap.put(t.getCoordinate(),t),t},interfaces_:function(){return[]},getClass:function(){return xr}}),e(Er.prototype,{findNodesOfDegree:function(t){for(var e=new I,n=this.nodeIterator();n.hasNext();){var i=n.next();i.getDegree()===t&&e.add(i)}return e},dirEdgeIterator:function(){return this.dirEdges.iterator()},edgeIterator:function(){return this.edges.iterator()},remove:function(){if(arguments[0]instanceof pr){var t=arguments[0];this.remove(t.getDirEdge(0)),this.remove(t.getDirEdge(1)),this.edges.remove(t),t.remove()}else if(arguments[0]instanceof gr){var e=arguments[0],n=e.getSym();null!==n&&n.setSym(null),e.getFromNode().remove(e),e.remove(),this.dirEdges.remove(e)}else if(arguments[0]instanceof mr){for(var i=arguments[0],r=i.getOutEdges().getEdges(),s=r.iterator();s.hasNext();){var o=s.next(),n=o.getSym();null!==n&&this.remove(n),this.dirEdges.remove(o);var a=o.getEdge();null!==a&&this.edges.remove(a)}this.nodeMap.remove(i.getCoordinate()),i.remove()}},findNode:function(t){return this.nodeMap.find(t)},getEdges:function(){return this.edges},nodeIterator:function(){return this.nodeMap.iterator()},contains:function(){if(arguments[0]instanceof pr){var t=arguments[0];return this.edges.contains(t)}if(arguments[0]instanceof gr){var e=arguments[0];return this.dirEdges.contains(e)}},add:function(){if(arguments[0]instanceof mr){var t=arguments[0];this.nodeMap.add(t)}else if(arguments[0]instanceof pr){var e=arguments[0];this.edges.add(e),this.add(e.getDirEdge(0)),this.add(e.getDirEdge(1))}else if(arguments[0]instanceof gr){var n=arguments[0];this.dirEdges.add(n)}},getNodes:function(){return this.nodeMap.values()},interfaces_:function(){return[]},getClass:function(){return Er}}),h(Ir,Er),e(Ir.prototype,{addEdge:function(t){if(t.isEmpty())return null;var e=H.removeRepeatedPoints(t.getCoordinates());if(e.length<=1)return null;var n=e[0],i=e[e.length-1],r=this.getNode(n),s=this.getNode(i),o=new dr(r,s,e[1],!0),a=new dr(s,r,e[e.length-2],!1),u=new yr(t);u.setDirectedEdges(o,a),this.add(u)},getNode:function(t){var e=this.findNode(t);return null===e&&(e=new mr(t),this.add(e)),e},interfaces_:function(){return[]},getClass:function(){return Ir}}),e(Nr.prototype,{buildEdgeStringsForUnprocessedNodes:function(){for(var t=this.graph.getNodes().iterator();t.hasNext();){var e=t.next();e.isMarked()||(f.isTrue(2===e.getDegree()),this.buildEdgeStringsStartingAt(e),e.setMarked(!0))}},buildEdgeStringsForNonDegree2Nodes:function(){for(var t=this.graph.getNodes().iterator();t.hasNext();){var e=t.next();2!==e.getDegree()&&(this.buildEdgeStringsStartingAt(e),e.setMarked(!0))}},buildEdgeStringsForObviousStartNodes:function(){this.buildEdgeStringsForNonDegree2Nodes()},getMergedLineStrings:function(){return this.merge(),this.mergedLineStrings},buildEdgeStringsStartingAt:function(t){for(var e=t.getOutEdges().iterator();e.hasNext();){var n=e.next();n.getEdge().isMarked()||this.edgeStrings.add(this.buildEdgeStringStartingWith(n))}},merge:function(){if(null!==this.mergedLineStrings)return null;fr.setMarked(this.graph.nodeIterator(),!1),fr.setMarked(this.graph.edgeIterator(),!1),this.edgeStrings=new I,this.buildEdgeStringsForObviousStartNodes(),this.buildEdgeStringsForIsolatedLoops(),this.mergedLineStrings=new I;for(var t=this.edgeStrings.iterator();t.hasNext();){var e=t.next();this.mergedLineStrings.add(e.toLineString())}},buildEdgeStringStartingWith:function(t){var e=new cr(this.factory),n=t;do e.add(n),n.getEdge().setMarked(!0),n=n.getNext();while(null!==n&&n!==t);return e},add:function(){if(arguments[0]instanceof B){var t=arguments[0];t.apply({interfaces_:function(){return[q]},filter:function(t){t instanceof St&&this.add(t)}})}else if(R(arguments[0],v)){var e=arguments[0];this.mergedLineStrings=null;for(var n=e.iterator();n.hasNext();){
var i=n.next();this.add(i)}}else if(arguments[0]instanceof St){var r=arguments[0];null===this.factory&&(this.factory=r.getFactory()),this.graph.addEdge(r)}},buildEdgeStringsForIsolatedLoops:function(){this.buildEdgeStringsForUnprocessedNodes()},interfaces_:function(){return[]},getClass:function(){return Nr}});var wo=Object.freeze({LineMerger:Nr}),Lo=Object.freeze({OverlayOp:ii});h(Cr,gr),e(Cr.prototype,{getNext:function(){return this.next},isInRing:function(){return null!==this.edgeRing},setRing:function(t){this.edgeRing=t},setLabel:function(t){this.label=t},getLabel:function(){return this.label},setNext:function(t){this.next=t},getRing:function(){return this.edgeRing},interfaces_:function(){return[]},getClass:function(){return Cr}}),h(Sr,pr),e(Sr.prototype,{getLine:function(){return this.line},interfaces_:function(){return[]},getClass:function(){return Sr}}),e(wr.prototype,{isIncluded:function(){return this._isIncluded},getCoordinates:function(){if(null===this.ringPts){for(var t=new N,e=this.deList.iterator();e.hasNext();){var n=e.next(),i=n.getEdge();wr.addEdge(i.getLine().getCoordinates(),n.getEdgeDirection(),t)}this.ringPts=t.toCoordinateArray()}return this.ringPts},isIncludedSet:function(){return this._isIncludedSet},isValid:function(){return this.getCoordinates(),this.ringPts.length<=3?!1:(this.getRing(),this.ring.isValid())},build:function(t){var e=t;do this.add(e),e.setRing(this),e=e.getNext(),f.isTrue(null!==e,"found null DE in ring"),f.isTrue(e===t||!e.isInRing(),"found DE already in ring");while(e!==t)},isOuterHole:function(){return this._isHole?!this.hasShell():!1},getPolygon:function(){var t=null;if(null!==this.holes){t=new Array(this.holes.size()).fill(null);for(var e=0;e<this.holes.size();e++)t[e]=this.holes.get(e)}var n=this.factory.createPolygon(this.ring,t);return n},isHole:function(){return this._isHole},isProcessed:function(){return this._isProcessed},addHole:function(){if(arguments[0]instanceof bt){var t=arguments[0];null===this.holes&&(this.holes=new I),this.holes.add(t)}else if(arguments[0]instanceof wr){var e=arguments[0];e.setShell(this);var n=e.getRing();null===this.holes&&(this.holes=new I),this.holes.add(n)}},setIncluded:function(t){this._isIncluded=t,this._isIncludedSet=!0},getOuterHole:function(){if(this.isHole())return null;for(var t=0;t<this.deList.size();t++){var e=this.deList.get(t),n=e.getSym().getRing();if(n.isOuterHole())return n}return null},computeHole:function(){var t=this.getRing();this._isHole=he.isCCW(t.getCoordinates())},hasShell:function(){return null!==this.shell},isOuterShell:function(){return null!==this.getOuterHole()},getLineString:function(){return this.getCoordinates(),this.factory.createLineString(this.ringPts)},toString:function(){return se.toLineString(new Gt(this.getCoordinates()))},getShell:function(){return this.isHole()?this.shell:this},add:function(t){this.deList.add(t)},getRing:function(){if(null!==this.ring)return this.ring;this.getCoordinates(),this.ringPts.length<3&&A.out.println(this.ringPts);try{this.ring=this.factory.createLinearRing(this.ringPts)}catch(t){if(!(t instanceof S))throw t;A.out.println(this.ringPts)}finally{}return this.ring},updateIncluded:function(){if(this.isHole())return null;for(var t=0;t<this.deList.size();t++){var e=this.deList.get(t),n=e.getSym().getRing().getShell();if(null!==n&&n.isIncludedSet())return this.setIncluded(!n.isIncluded()),null}},setShell:function(t){this.shell=t},setProcessed:function(t){this._isProcessed=t},interfaces_:function(){return[]},getClass:function(){return wr}}),wr.findDirEdgesInRing=function(t){var e=t,n=new I;do n.add(e),e=e.getNext(),f.isTrue(null!==e,"found null DE in ring"),f.isTrue(e===t||!e.isInRing(),"found DE already in ring");while(e!==t);return n},wr.addEdge=function(t,e,n){if(e)for(var i=0;i<t.length;i++)n.add(t[i],!1);else for(var i=t.length-1;i>=0;i--)n.add(t[i],!1)},wr.findEdgeRingContaining=function(t,e){for(var n=t.getRing(),i=n.getEnvelopeInternal(),r=n.getCoordinateN(0),s=null,o=null,a=e.iterator();a.hasNext();){var u=a.next(),l=u.getRing(),h=l.getEnvelopeInternal();if(!h.equals(i)&&h.contains(i)){r=H.ptNotInList(n.getCoordinates(),l.getCoordinates());var c=!1;he.isPointInRing(r,l.getCoordinates())&&(c=!0),c&&(null===s||o.contains(h))&&(s=u,o=s.getRing().getEnvelopeInternal())}}return s},e(Lr.prototype,{compare:function(t,e){var n=t,i=e;return n.getRing().getEnvelope().compareTo(i.getRing().getEnvelope())},interfaces_:function(){return[a]},getClass:function(){return Lr}}),wr.EnvelopeComparator=Lr,h(Rr,Er),e(Rr.prototype,{findEdgeRing:function(t){var e=new wr(this.factory);return e.build(t),e},computeDepthParity:function(){if(0===arguments.length)for(;;){var t=null;if(null===t)return null;this.computeDepthParity(t)}else if(1===arguments.length){arguments[0]}},computeNextCWEdges:function(){for(var t=this.nodeIterator();t.hasNext();){var e=t.next();Rr.computeNextCWEdges(e)}},addEdge:function(t){if(t.isEmpty())return null;var e=H.removeRepeatedPoints(t.getCoordinates());if(e.length<2)return null;var n=e[0],i=e[e.length-1],r=this.getNode(n),s=this.getNode(i),o=new Cr(r,s,e[1],!0),a=new Cr(s,r,e[e.length-2],!1),u=new Sr(t);u.setDirectedEdges(o,a),this.add(u)},deleteCutEdges:function(){this.computeNextCWEdges(),Rr.findLabeledEdgeRings(this.dirEdges);for(var t=new I,e=this.dirEdges.iterator();e.hasNext();){var n=e.next();if(!n.isMarked()){var i=n.getSym();if(n.getLabel()===i.getLabel()){n.setMarked(!0),i.setMarked(!0);var r=n.getEdge();t.add(r.getLine())}}}return t},getEdgeRings:function(){this.computeNextCWEdges(),Rr.label(this.dirEdges,-1);var t=Rr.findLabeledEdgeRings(this.dirEdges);this.convertMaximalToMinimalEdgeRings(t);for(var e=new I,n=this.dirEdges.iterator();n.hasNext();){var i=n.next();if(!i.isMarked()&&!i.isInRing()){var r=this.findEdgeRing(i);e.add(r)}}return e},getNode:function(t){var e=this.findNode(t);return null===e&&(e=new mr(t),this.add(e)),e},convertMaximalToMinimalEdgeRings:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next(),i=n.getLabel(),r=Rr.findIntersectionNodes(n,i);if(null!==r)for(var s=r.iterator();s.hasNext();){var o=s.next();Rr.computeNextCCWEdges(o,i)}}},deleteDangles:function(){for(var t=this.findNodesOfDegree(1),e=new J,n=new pe,i=t.iterator();i.hasNext();)n.push(i.next());for(;!n.isEmpty();){var r=n.pop();Rr.deleteAllEdges(r);for(var s=r.getOutEdges().getEdges(),i=s.iterator();i.hasNext();){var o=i.next();o.setMarked(!0);var a=o.getSym();null!==a&&a.setMarked(!0);var u=o.getEdge();e.add(u.getLine());var l=o.getToNode();1===Rr.getDegreeNonDeleted(l)&&n.push(l)}}return e},interfaces_:function(){return[]},getClass:function(){return Rr}}),Rr.findLabeledEdgeRings=function(t){for(var e=new I,n=1,i=t.iterator();i.hasNext();){var r=i.next();if(!(r.isMarked()||r.getLabel()>=0)){e.add(r);var s=wr.findDirEdgesInRing(r);Rr.label(s,n),n++}}return e},Rr.getDegreeNonDeleted=function(t){for(var e=t.getOutEdges().getEdges(),n=0,i=e.iterator();i.hasNext();){var r=i.next();r.isMarked()||n++}return n},Rr.deleteAllEdges=function(t){for(var e=t.getOutEdges().getEdges(),n=e.iterator();n.hasNext();){var i=n.next();i.setMarked(!0);var r=i.getSym();null!==r&&r.setMarked(!0)}},Rr.label=function(t,e){for(var n=t.iterator();n.hasNext();){var i=n.next();i.setLabel(e)}},Rr.computeNextCWEdges=function(t){for(var e=t.getOutEdges(),n=null,i=null,r=e.getEdges().iterator();r.hasNext();){var s=r.next();if(!s.isMarked()){if(null===n&&(n=s),null!==i){var o=i.getSym();o.setNext(s)}i=s}}if(null!==i){var o=i.getSym();o.setNext(n)}},Rr.computeNextCCWEdges=function(t,e){for(var n=t.getOutEdges(),i=null,r=null,s=n.getEdges(),o=s.size()-1;o>=0;o--){var a=s.get(o),u=a.getSym(),l=null;a.getLabel()===e&&(l=a);var h=null;u.getLabel()===e&&(h=u),null===l&&null===h||(null!==h&&(r=h),null!==l&&(null!==r&&(r.setNext(l),r=null),null===i&&(i=l)))}null!==r&&(f.isTrue(null!==i),r.setNext(i))},Rr.getDegree=function(t,e){for(var n=t.getOutEdges().getEdges(),i=0,r=n.iterator();r.hasNext();){var s=r.next();s.getLabel()===e&&i++}return i},Rr.findIntersectionNodes=function(t,e){var n=t,i=null;do{var r=n.getFromNode();Rr.getDegree(r,e)>1&&(null===i&&(i=new I),i.add(r)),n=n.getNext(),f.isTrue(null!==n,"found null DE in ring"),f.isTrue(n===t||!n.isInRing(),"found DE already in ring")}while(n!==t);return i},e(Tr.prototype,{getGeometry:function(){return null===this.geomFactory&&(this.geomFactory=new ie),this.polygonize(),this.extractOnlyPolygonal?this.geomFactory.buildGeometry(this.polyList):this.geomFactory.createGeometryCollection(ie.toGeometryArray(this.polyList))},getInvalidRingLines:function(){return this.polygonize(),this.invalidRingLines},findValidRings:function(t,e,n){for(var i=t.iterator();i.hasNext();){var r=i.next();r.isValid()?e.add(r):n.add(r.getLineString())}},polygonize:function(){if(null!==this.polyList)return null;if(this.polyList=new I,null===this.graph)return null;this.dangles=this.graph.deleteDangles(),this.cutEdges=this.graph.deleteCutEdges();var t=this.graph.getEdgeRings(),e=new I;this.invalidRingLines=new I,this.isCheckingRingsValid?this.findValidRings(t,e,this.invalidRingLines):e=t,this.findShellsAndHoles(e),Tr.assignHolesToShells(this.holeList,this.shellList),ho.sort(this.shellList,new wr.EnvelopeComparator);var n=!0;this.extractOnlyPolygonal&&(Tr.findDisjointShells(this.shellList),n=!1),this.polyList=Tr.extractPolygons(this.shellList,n)},getDangles:function(){return this.polygonize(),this.dangles},getCutEdges:function(){return this.polygonize(),this.cutEdges},getPolygons:function(){return this.polygonize(),this.polyList},add:function(){if(R(arguments[0],v))for(var t=arguments[0],e=t.iterator();e.hasNext();){var n=e.next();this.add(n)}else if(arguments[0]instanceof St){var i=arguments[0];this.geomFactory=i.getFactory(),null===this.graph&&(this.graph=new Rr(this.geomFactory)),this.graph.addEdge(i)}else if(arguments[0]instanceof B){var r=arguments[0];r.apply(this.lineStringAdder)}},setCheckRingsValid:function(t){this.isCheckingRingsValid=t},findShellsAndHoles:function(t){this.holeList=new I,this.shellList=new I;for(var e=t.iterator();e.hasNext();){var n=e.next();n.computeHole(),n.isHole()?this.holeList.add(n):this.shellList.add(n)}},interfaces_:function(){return[]},getClass:function(){return Tr}}),Tr.findOuterShells=function(t){for(var e=t.iterator();e.hasNext();){var n=e.next(),i=n.getOuterHole();null===i||i.isProcessed()||(n.setIncluded(!0),i.setProcessed(!0))}},Tr.extractPolygons=function(t,e){for(var n=new I,i=t.iterator();i.hasNext();){var r=i.next();(e||r.isIncluded())&&n.add(r.getPolygon())}return n},Tr.assignHolesToShells=function(t,e){for(var n=t.iterator();n.hasNext();){var i=n.next();Tr.assignHoleToShell(i,e)}},Tr.assignHoleToShell=function(t,e){var n=wr.findEdgeRingContaining(t,e);null!==n&&n.addHole(t)},Tr.findDisjointShells=function(t){Tr.findOuterShells(t);var e=null;do{e=!1;for(var n=t.iterator();n.hasNext();){var i=n.next();i.isIncludedSet()||(i.updateIncluded(),i.isIncludedSet()||(e=!0))}}while(e)},e(Pr.prototype,{filter:function(t){t instanceof St&&this.p.add(t)},interfaces_:function(){return[q]},getClass:function(){return Pr}}),Tr.LineStringAdder=Pr;var Ro=Object.freeze({Polygonizer:Tr});e(br.prototype,{createEdgeEndForNext:function(t,e,n,i){var r=n.segmentIndex+1;if(r>=t.getNumPoints()&&null===i)return null;var s=t.getCoordinate(r);null!==i&&i.segmentIndex===n.segmentIndex&&(s=i.coord);var o=new En(t,n.coord,s,new gn(t.getLabel()));e.add(o)},createEdgeEndForPrev:function(t,e,n,i){var r=n.segmentIndex;if(0===n.dist){if(0===r)return null;r--}var s=t.getCoordinate(r);null!==i&&i.segmentIndex>=r&&(s=i.coord);var o=new gn(t.getLabel());o.flip();var a=new En(t,n.coord,s,o);e.add(a)},computeEdgeEnds:function(){if(1===arguments.length){for(var t=arguments[0],e=new I,n=t;n.hasNext();){var i=n.next();this.computeEdgeEnds(i,e)}return e}if(2===arguments.length){var r=arguments[0],s=arguments[1],o=r.getEdgeIntersectionList();o.addEndpoints();var a=o.iterator(),u=null,l=null;if(!a.hasNext())return null;var h=a.next();do u=l,l=h,h=null,a.hasNext()&&(h=a.next()),null!==l&&(this.createEdgeEndForPrev(r,s,l,u),this.createEdgeEndForNext(r,s,l,h));while(null!==l)}},interfaces_:function(){return[]},getClass:function(){return br}}),h(Or,En),e(Or.prototype,{insert:function(t){this.edgeEnds.add(t)},print:function(t){t.println("EdgeEndBundle--> Label: "+this.label);for(var e=this.iterator();e.hasNext();){var n=e.next();n.print(t),t.println()}},iterator:function(){return this.edgeEnds.iterator()},getEdgeEnds:function(){return this.edgeEnds},computeLabelOn:function(t,e){for(var n=0,i=!1,r=this.iterator();r.hasNext();){var s=r.next(),o=s.getLabel().getLocation(t);o===L.BOUNDARY&&n++,o===L.INTERIOR&&(i=!0)}var o=L.NONE;i&&(o=L.INTERIOR),n>0&&(o=$n.determineBoundary(e,n)),this.label.setLocation(t,o)},computeLabelSide:function(t,e){for(var n=this.iterator();n.hasNext();){var i=n.next();if(i.getLabel().isArea()){var r=i.getLabel().getLocation(t,e);if(r===L.INTERIOR)return this.label.setLocation(t,e,L.INTERIOR),null;r===L.EXTERIOR&&this.label.setLocation(t,e,L.EXTERIOR)}}},getLabel:function(){return this.label},computeLabelSides:function(t){this.computeLabelSide(t,cn.LEFT),this.computeLabelSide(t,cn.RIGHT)},updateIM:function(t){Jn.updateIM(this.label,t)},computeLabel:function(t){for(var e=!1,n=this.iterator();n.hasNext();){var i=n.next();i.getLabel().isArea()&&(e=!0)}e?this.label=new gn(L.NONE,L.NONE,L.NONE):this.label=new gn(L.NONE);for(var r=0;2>r;r++)this.computeLabelOn(r,t),e&&this.computeLabelSides(r)},interfaces_:function(){return[]},getClass:function(){return Or}}),h(_r,Pn),e(_r.prototype,{updateIM:function(t){for(var e=this.iterator();e.hasNext();){var n=e.next();n.updateIM(t)}},insert:function(t){var e=this.edgeMap.get(t);null===e?(e=new Or(t),this.insertEdgeEnd(t,e)):e.insert(t)},interfaces_:function(){return[]},getClass:function(){return _r}}),h(Mr,yn),e(Mr.prototype,{updateIMFromEdges:function(t){this.edges.updateIM(t)},computeIM:function(t){t.setAtLeastIfValid(this.label.getLocation(0),this.label.getLocation(1),0)},interfaces_:function(){return[]},getClass:function(){return Mr}}),h(Dr,Nn),e(Dr.prototype,{createNode:function(t){return new Mr(t,new _r)},interfaces_:function(){return[]},getClass:function(){return Dr}}),e(Ar.prototype,{insertEdgeEnds:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.nodes.add(n)}},computeProperIntersectionIM:function(t,e){var n=this.arg[0].getGeometry().getDimension(),i=this.arg[1].getGeometry().getDimension(),r=t.hasProperIntersection(),s=t.hasProperInteriorIntersection();2===n&&2===i?r&&e.setAtLeast("212101212"):2===n&&1===i?(r&&e.setAtLeast("FFF0FFFF2"),s&&e.setAtLeast("1FFFFF1FF")):1===n&&2===i?(r&&e.setAtLeast("F0FFFFFF2"),s&&e.setAtLeast("1F1FFFFFF")):1===n&&1===i&&s&&e.setAtLeast("0FFFFFFFF")},labelIsolatedEdges:function(t,e){for(var n=this.arg[t].getEdgeIterator();n.hasNext();){var i=n.next();i.isIsolated()&&(this.labelIsolatedEdge(i,e,this.arg[e].getGeometry()),this.isolatedEdges.add(i))}},labelIsolatedEdge:function(t,e,n){if(n.getDimension()>0){var i=this.ptLocator.locate(t.getCoordinate(),n);t.getLabel().setAllLocations(e,i)}else t.getLabel().setAllLocations(e,L.EXTERIOR)},computeIM:function(){var t=new fe;if(t.set(L.EXTERIOR,L.EXTERIOR,2),!this.arg[0].getGeometry().getEnvelopeInternal().intersects(this.arg[1].getGeometry().getEnvelopeInternal()))return this.computeDisjointIM(t),t;this.arg[0].computeSelfNodes(this.li,!1),this.arg[1].computeSelfNodes(this.li,!1);var e=this.arg[0].computeEdgeIntersections(this.arg[1],this.li,!1);this.computeIntersectionNodes(0),this.computeIntersectionNodes(1),this.copyNodesAndLabels(0),this.copyNodesAndLabels(1),this.labelIsolatedNodes(),this.computeProperIntersectionIM(e,t);var n=new br,i=n.computeEdgeEnds(this.arg[0].getEdgeIterator());this.insertEdgeEnds(i);var r=n.computeEdgeEnds(this.arg[1].getEdgeIterator());return this.insertEdgeEnds(r),this.labelNodeEdges(),this.labelIsolatedEdges(0,1),this.labelIsolatedEdges(1,0),this.updateIM(t),t},labelNodeEdges:function(){for(var t=this.nodes.iterator();t.hasNext();){var e=t.next();e.getEdges().computeLabelling(this.arg)}},copyNodesAndLabels:function(t){for(var e=this.arg[t].getNodeIterator();e.hasNext();){var n=e.next(),i=this.nodes.addNode(n.getCoordinate());i.setLabel(t,n.getLabel().getLocation(t))}},labelIntersectionNodes:function(t){for(var e=this.arg[t].getEdgeIterator();e.hasNext();)for(var n=e.next(),i=n.getLabel().getLocation(t),r=n.getEdgeIntersectionList().iterator();r.hasNext();){var s=r.next(),o=this.nodes.find(s.coord);o.getLabel().isNull(t)&&(i===L.BOUNDARY?o.setLabelBoundary(t):o.setLabel(t,L.INTERIOR))}},labelIsolatedNode:function(t,e){var n=this.ptLocator.locate(t.getCoordinate(),this.arg[e].getGeometry());t.getLabel().setAllLocations(e,n)},computeIntersectionNodes:function(t){for(var e=this.arg[t].getEdgeIterator();e.hasNext();)for(var n=e.next(),i=n.getLabel().getLocation(t),r=n.getEdgeIntersectionList().iterator();r.hasNext();){var s=r.next(),o=this.nodes.addNode(s.coord);i===L.BOUNDARY?o.setLabelBoundary(t):o.getLabel().isNull(t)&&o.setLabel(t,L.INTERIOR)}},labelIsolatedNodes:function(){for(var t=this.nodes.iterator();t.hasNext();){var e=t.next(),n=e.getLabel();f.isTrue(n.getGeometryCount()>0,"node with empty label found"),e.isIsolated()&&(n.isNull(0)?this.labelIsolatedNode(e,0):this.labelIsolatedNode(e,1))}},updateIM:function(t){for(var e=this.isolatedEdges.iterator();e.hasNext();){var n=e.next();n.updateIM(t)}for(var i=this.nodes.iterator();i.hasNext();){var r=i.next();r.updateIM(t),r.updateIMFromEdges(t)}},computeDisjointIM:function(t){var e=this.arg[0].getGeometry();e.isEmpty()||(t.set(L.INTERIOR,L.EXTERIOR,e.getDimension()),t.set(L.BOUNDARY,L.EXTERIOR,e.getBoundaryDimension()));var n=this.arg[1].getGeometry();n.isEmpty()||(t.set(L.EXTERIOR,L.INTERIOR,n.getDimension()),t.set(L.EXTERIOR,L.BOUNDARY,n.getBoundaryDimension()))},interfaces_:function(){return[]},getClass:function(){return Ar}}),e(Fr.prototype,{isContainedInBoundary:function(t){if(t instanceof Tt)return!1;if(t instanceof Lt)return this.isPointContainedInBoundary(t);if(t instanceof St)return this.isLineStringContainedInBoundary(t);for(var e=0;e<t.getNumGeometries();e++){var n=t.getGeometryN(e);if(!this.isContainedInBoundary(n))return!1}return!0},isLineSegmentContainedInBoundary:function(t,e){if(t.equals(e))return this.isPointContainedInBoundary(t);if(t.x===e.x){if(t.x===this.rectEnv.getMinX()||t.x===this.rectEnv.getMaxX())return!0}else if(t.y===e.y&&(t.y===this.rectEnv.getMinY()||t.y===this.rectEnv.getMaxY()))return!0;return!1},isLineStringContainedInBoundary:function(t){for(var e=t.getCoordinateSequence(),n=new g,i=new g,r=0;r<e.size()-1;r++)if(e.getCoordinate(r,n),e.getCoordinate(r+1,i),!this.isLineSegmentContainedInBoundary(n,i))return!1;return!0},isPointContainedInBoundary:function(){if(arguments[0]instanceof Lt){var t=arguments[0];return this.isPointContainedInBoundary(t.getCoordinate())}if(arguments[0]instanceof g){var e=arguments[0];return e.x===this.rectEnv.getMinX()||e.x===this.rectEnv.getMaxX()||e.y===this.rectEnv.getMinY()||e.y===this.rectEnv.getMaxY()}},contains:function(t){return this.rectEnv.contains(t.getEnvelopeInternal())?!this.isContainedInBoundary(t):!1},interfaces_:function(){return[]},getClass:function(){return Fr}}),Fr.contains=function(t,e){var n=new Fr(t);return n.contains(e)},e(Gr.prototype,{intersects:function(t,e){var n=new C(t,e);if(!this.rectEnv.intersects(n))return!1;if(this.rectEnv.intersects(t))return!0;if(this.rectEnv.intersects(e))return!0;if(t.compareTo(e)>0){var i=t;t=e,e=i}var r=!1;return e.y>t.y&&(r=!0),r?this.li.computeIntersection(t,e,this.diagDown0,this.diagDown1):this.li.computeIntersection(t,e,this.diagUp0,this.diagUp1),!!this.li.hasIntersection()},interfaces_:function(){return[]},getClass:function(){return Gr}}),e(qr.prototype,{applyTo:function(t){for(var e=0;e<t.getNumGeometries()&&!this._isDone;e++){var n=t.getGeometryN(e);if(n instanceof ft)this.applyTo(n);else if(this.visit(n),this.isDone())return this._isDone=!0,null}},interfaces_:function(){return[]},getClass:function(){return qr}}),e(Br.prototype,{intersects:function(t){if(!this.rectEnv.intersects(t.getEnvelopeInternal()))return!1;var e=new zr(this.rectEnv);if(e.applyTo(t),e.intersects())return!0;var n=new Vr(this.rectangle);if(n.applyTo(t),n.containsPoint())return!0;var i=new kr(this.rectangle);return i.applyTo(t),!!i.intersects()},interfaces_:function(){return[]},getClass:function(){return Br}}),Br.intersects=function(t,e){var n=new Br(t);return n.intersects(e)},h(zr,qr),e(zr.prototype,{isDone:function(){return this._intersects===!0},visit:function(t){var e=t.getEnvelopeInternal();return this.rectEnv.intersects(e)?this.rectEnv.contains(e)?(this._intersects=!0,null):e.getMinX()>=this.rectEnv.getMinX()&&e.getMaxX()<=this.rectEnv.getMaxX()?(this._intersects=!0,null):e.getMinY()>=this.rectEnv.getMinY()&&e.getMaxY()<=this.rectEnv.getMaxY()?(this._intersects=!0,null):void 0:null},intersects:function(){return this._intersects},interfaces_:function(){return[]},getClass:function(){return zr}}),h(Vr,qr),e(Vr.prototype,{isDone:function(){return this._containsPoint===!0},visit:function(t){if(!(t instanceof Tt))return null;var e=t.getEnvelopeInternal();if(!this.rectEnv.intersects(e))return null;for(var n=new g,i=0;4>i;i++)if(this.rectSeq.getCoordinate(i,n),e.contains(n)&&Tn.containsPointInPolygon(n,t))return this._containsPoint=!0,null},containsPoint:function(){return this._containsPoint},interfaces_:function(){return[]},getClass:function(){return Vr}}),h(kr,qr),e(kr.prototype,{intersects:function(){return this.hasIntersection},isDone:function(){return this.hasIntersection===!0},visit:function(t){var e=t.getEnvelopeInternal();if(!this.rectEnv.intersects(e))return null;var n=kn.getLines(t);this.checkIntersectionWithLineStrings(n)},checkIntersectionWithLineStrings:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();if(this.checkIntersectionWithSegments(n),this.hasIntersection)return null}},checkIntersectionWithSegments:function(t){for(var e=t.getCoordinateSequence(),n=1;n<e.size();n++)if(e.getCoordinate(n-1,this.p0),e.getCoordinate(n,this.p1),this.rectIntersector.intersects(this.p0,this.p1))return this.hasIntersection=!0,null},interfaces_:function(){return[]},getClass:function(){return kr}}),h(Yr,ti),e(Yr.prototype,{getIntersectionMatrix:function(){return this._relate.computeIM()},interfaces_:function(){return[]},getClass:function(){return Yr}}),Yr.covers=function(t,e){return t.getEnvelopeInternal().covers(e.getEnvelopeInternal())?t.isRectangle()?!0:Yr.relate(t,e).isCovers():!1},Yr.intersects=function(t,e){return t.getEnvelopeInternal().intersects(e.getEnvelopeInternal())?t.isRectangle()?Br.intersects(t,e):e.isRectangle()?Br.intersects(e,t):Yr.relate(t,e).isIntersects():!1},Yr.touches=function(t,e){return t.getEnvelopeInternal().intersects(e.getEnvelopeInternal())?Yr.relate(t,e).isTouches(t.getDimension(),e.getDimension()):!1},Yr.within=function(t,e){return e.contains(t)},Yr.coveredBy=function(t,e){return Yr.covers(e,t)},Yr.relate=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=new Yr(t,e),i=n.getIntersectionMatrix();return i}if(3===arguments.length){if("string"==typeof arguments[2]&&arguments[0]instanceof B&&arguments[1]instanceof B){var r=arguments[0],s=arguments[1],o=arguments[2];return Yr.relateWithCheck(r,s).matches(o)}if(R(arguments[2],V)&&arguments[0]instanceof B&&arguments[1]instanceof B){var a=arguments[0],u=arguments[1],l=arguments[2],n=new Yr(a,u,l),i=n.getIntersectionMatrix();return i}}},Yr.overlaps=function(t,e){return t.getEnvelopeInternal().intersects(e.getEnvelopeInternal())?Yr.relate(t,e).isOverlaps(t.getDimension(),e.getDimension()):!1},Yr.disjoint=function(t,e){return!t.intersects(e)},Yr.relateWithCheck=function(t,e){return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),Yr.relate(t,e)},Yr.crosses=function(t,e){return t.getEnvelopeInternal().intersects(e.getEnvelopeInternal())?Yr.relate(t,e).isCrosses(t.getDimension(),e.getDimension()):!1},Yr.contains=function(t,e){return t.getEnvelopeInternal().contains(e.getEnvelopeInternal())?t.isRectangle()?Fr.contains(t,e):Yr.relate(t,e).isContains():!1};var To=Object.freeze({RelateOp:Yr});e(Ur.prototype,{extractElements:function(t,e){if(null===t)return null;for(var n=0;n<t.getNumGeometries();n++){var i=t.getGeometryN(n);this.skipEmpty&&i.isEmpty()||e.add(i)}},combine:function(){for(var t=new I,e=this.inputGeoms.iterator();e.hasNext();){var n=e.next();this.extractElements(n,t)}return 0===t.size()?null!==this.geomFactory?this.geomFactory.createGeometryCollection(null):null:this.geomFactory.buildGeometry(t)},interfaces_:function(){return[]},getClass:function(){return Ur}}),Ur.combine=function(){if(1===arguments.length){var t=arguments[0],e=new Ur(t);return e.combine()}if(2===arguments.length){var n=arguments[0],i=arguments[1],e=new Ur(Ur.createList(n,i));return e.combine()}if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],e=new Ur(Ur.createList(r,s,o));return e.combine()}},Ur.extractFactory=function(t){return t.isEmpty()?null:t.iterator().next().getFactory()},Ur.createList=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=new I;return n.add(t),n.add(e),n}if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2],n=new I;return n.add(i),n.add(r),n.add(s),n}},e(Xr.prototype,{union:function(){for(var t=new Te,e=new at,n=0;n<this.pointGeom.getNumGeometries();n++){var i=this.pointGeom.getGeometryN(n),r=i.getCoordinate(),s=t.locate(r,this.otherGeom);s===L.EXTERIOR&&e.add(r)}if(0===e.size())return this.otherGeom;var o=null,a=H.toCoordinateArray(e);return o=1===a.length?this.geomFact.createPoint(a[0]):this.geomFact.createMultiPointFromCoords(a),Ur.combine(o,this.otherGeom)},interfaces_:function(){return[]},getClass:function(){return Xr}}),Xr.union=function(t,e){var n=new Xr(t,e);return n.union()},e(Hr.prototype,{filter:function(t){-1!==this.sortIndex&&t.getSortIndex()!==this.sortIndex||this.comps.add(t)},interfaces_:function(){return[ht]},getClass:function(){return Hr}}),Hr.extract=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return Hr.extract(t,e,new I)}if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];return n.getSortIndex()===i?r.add(n):n instanceof ft&&n.apply(new Hr(i,r)),r}},e(Wr.prototype,{reduceToGeometries:function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next(),r=null;R(i,y)?r=this.unionTree(i):i instanceof B&&(r=i),e.add(r)}return e},extractByEnvelope:function(t,e,n){for(var i=new I,r=0;r<e.getNumGeometries();r++){var s=e.getGeometryN(r);s.getEnvelopeInternal().intersects(t)?i.add(s):n.add(s)}return this.geomFactory.buildGeometry(i)},unionOptimized:function(t,e){var n=t.getEnvelopeInternal(),i=e.getEnvelopeInternal();if(!n.intersects(i)){var r=Ur.combine(t,e);return r}if(t.getNumGeometries()<=1&&e.getNumGeometries()<=1)return this.unionActual(t,e);var s=n.intersection(i);return this.unionUsingEnvelopeIntersection(t,e,s)},union:function(){if(null===this.inputPolys)throw new IllegalStateException("union() method cannot be called twice");if(this.inputPolys.isEmpty())return null;this.geomFactory=this.inputPolys.iterator().next().getFactory();for(var t=new ke(Wr.STRTREE_NODE_CAPACITY),e=this.inputPolys.iterator();e.hasNext();){var n=e.next();t.insert(n.getEnvelopeInternal(),n)}this.inputPolys=null;var i=t.itemsTree(),r=this.unionTree(i);return r},binaryUnion:function(){if(1===arguments.length){var t=arguments[0];return this.binaryUnion(t,0,t.size())}if(3===arguments.length){var e=arguments[0],n=arguments[1],i=arguments[2];if(1>=i-n){var r=Wr.getGeometry(e,n);return this.unionSafe(r,null)}if(i-n===2)return this.unionSafe(Wr.getGeometry(e,n),Wr.getGeometry(e,n+1));var s=Math.trunc((i+n)/2),r=this.binaryUnion(e,n,s),o=this.binaryUnion(e,s,i);return this.unionSafe(r,o)}},repeatedUnion:function(t){for(var e=null,n=t.iterator();n.hasNext();){var i=n.next();e=null===e?i.copy():e.union(i)}return e},unionSafe:function(t,e){return null===t&&null===e?null:null===t?e.copy():null===e?t.copy():this.unionOptimized(t,e)},unionActual:function(t,e){return Wr.restrictToPolygons(t.union(e))},unionTree:function(t){var e=this.reduceToGeometries(t),n=this.binaryUnion(e);return n},unionUsingEnvelopeIntersection:function(t,e,n){var i=new I,r=this.extractByEnvelope(n,t,i),s=this.extractByEnvelope(n,e,i),o=this.unionActual(r,s);i.add(o);var a=Ur.combine(i);return a},bufferUnion:function(){if(1===arguments.length){var t=arguments[0],e=t.get(0).getFactory(),n=e.buildGeometry(t),i=n.buffer(0);return i}if(2===arguments.length){var r=arguments[0],s=arguments[1],e=r.getFactory(),n=e.createGeometryCollection([r,s]),i=n.buffer(0);return i}},interfaces_:function(){return[]},getClass:function(){return Wr}}),Wr.restrictToPolygons=function(t){if(R(t,Rt))return t;var e=or.getPolygons(t);return 1===e.size()?e.get(0):t.getFactory().createMultiPolygon(ie.toPolygonArray(e))},Wr.getGeometry=function(t,e){return e>=t.size()?null:t.get(e)},Wr.union=function(t){var e=new Wr(t);return e.union()},Wr.STRTREE_NODE_CAPACITY=4,e(jr.prototype,{unionNoOpt:function(t){var e=this.geomFact.createPoint();return si.overlayOp(t,e,ii.UNION)},unionWithNull:function(t,e){return null===t&&null===e?null:null===e?t:null===t?e:t.union(e)},extract:function(){if(R(arguments[0],v))for(var t=arguments[0],e=t.iterator();e.hasNext();){var n=e.next();this.extract(n)}else if(arguments[0]instanceof B){var i=arguments[0];null===this.geomFact&&(this.geomFact=i.getFactory()),Hr.extract(i,B.SORTINDEX_POLYGON,this.polygons),Hr.extract(i,B.SORTINDEX_LINESTRING,this.lines),Hr.extract(i,B.SORTINDEX_POINT,this.points)}},union:function t(){if(null===this.geomFact)return null;var e=null;if(this.points.size()>0){var n=this.geomFact.buildGeometry(this.points);e=this.unionNoOpt(n)}var i=null;if(this.lines.size()>0){var r=this.geomFact.buildGeometry(this.lines);i=this.unionNoOpt(r)}var s=null;this.polygons.size()>0&&(s=Wr.union(this.polygons));var o=this.unionWithNull(i,s),t=null;return t=null===e?o:null===o?e:Xr.union(e,o),null===t?this.geomFact.createGeometryCollection():t},interfaces_:function(){return[]},getClass:function(){return jr}}),jr.union=function(){if(1===arguments.length){if(R(arguments[0],v)){var t=arguments[0],e=new jr(t);return e.union()}if(arguments[0]instanceof B){var n=arguments[0],e=new jr(n);return e.union()}}else if(2===arguments.length){var i=arguments[0],r=arguments[1],e=new jr(i,r);return e.union()}};var Po=Object.freeze({UnaryUnionOp:jr});e(Kr.prototype,{visitInteriorRing:function(t,e){var n=t.getCoordinates(),i=n[0],r=Kr.findDifferentPoint(n,i),s=e.findEdgeInSameDirection(i,r),o=e.findEdgeEnd(s),a=null;o.getLabel().getLocation(0,cn.RIGHT)===L.INTERIOR?a=o:o.getSym().getLabel().getLocation(0,cn.RIGHT)===L.INTERIOR&&(a=o.getSym()),f.isTrue(null!==a,"unable to find dirEdge with Interior on RHS"),this.visitLinkedDirectedEdges(a)},visitShellInteriors:function(t,e){if(t instanceof Tt){var n=t;this.visitInteriorRing(n.getExteriorRing(),e)}if(t instanceof Ot)for(var i=t,r=0;r<i.getNumGeometries();r++){var n=i.getGeometryN(r);this.visitInteriorRing(n.getExteriorRing(),e)}},getCoordinate:function(){return this.disconnectedRingcoord},setInteriorEdgesInResult:function(t){for(var e=t.getEdgeEnds().iterator();e.hasNext();){var n=e.next();n.getLabel().getLocation(0,cn.RIGHT)===L.INTERIOR&&n.setInResult(!0)}},visitLinkedDirectedEdges:function(t){var e=t,n=t;do f.isTrue(null!==n,"found null Directed Edge"),n.setVisited(!0),n=n.getNext();while(n!==e)},buildEdgeRings:function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next();if(i.isInResult()&&null===i.getEdgeRing()){var r=new vn(i,this.geometryFactory);r.linkDirectedEdgesForMinimalEdgeRings();var s=r.buildMinimalRings();e.addAll(s)}}return e},hasUnvisitedShellEdge:function(t){for(var e=0;e<t.size();e++){var n=t.get(e);if(!n.isHole()){var i=n.getEdges(),r=i.get(0);if(r.getLabel().getLocation(0,cn.RIGHT)===L.INTERIOR)for(var s=0;s<i.size();s++)if(r=i.get(s),
!r.isVisited())return this.disconnectedRingcoord=r.getCoordinate(),!0}}return!1},isInteriorsConnected:function(){var t=new I;this.geomGraph.computeSplitEdges(t);var e=new Cn(new On);e.addEdges(t),this.setInteriorEdgesInResult(e),e.linkResultDirectedEdges();var n=this.buildEdgeRings(e.getEdgeEnds());return this.visitShellInteriors(this.geomGraph.getGeometry(),e),!this.hasUnvisitedShellEdge(n)},interfaces_:function(){return[]},getClass:function(){return Kr}}),Kr.findDifferentPoint=function(t,e){for(var n=0;n<t.length;n++)if(!t[n].equals(e))return t[n];return null},e(Zr.prototype,{hasChildren:function(){for(var t=0;2>t;t++)if(null!==this.subnode[t])return!0;return!1},isPrunable:function(){return!(this.hasChildren()||this.hasItems())},addAllItems:function(t){t.addAll(this.items);for(var e=0;2>e;e++)null!==this.subnode[e]&&this.subnode[e].addAllItems(t);return t},size:function(){for(var t=0,e=0;2>e;e++)null!==this.subnode[e]&&(t+=this.subnode[e].size());return t+this.items.size()},addAllItemsFromOverlapping:function(t,e){return null===t||this.isSearchMatch(t)?(e.addAll(this.items),null!==this.subnode[0]&&this.subnode[0].addAllItemsFromOverlapping(t,e),void(null!==this.subnode[1]&&this.subnode[1].addAllItemsFromOverlapping(t,e))):null},hasItems:function(){return!this.items.isEmpty()},remove:function(t,e){if(!this.isSearchMatch(t))return!1;for(var n=!1,i=0;2>i;i++)if(null!==this.subnode[i]&&(n=this.subnode[i].remove(t,e))){this.subnode[i].isPrunable()&&(this.subnode[i]=null);break}return n?n:n=this.items.remove(e)},getItems:function(){return this.items},depth:function(){for(var t=0,e=0;2>e;e++)if(null!==this.subnode[e]){var n=this.subnode[e].depth();n>t&&(t=n)}return t+1},nodeSize:function(){for(var t=0,e=0;2>e;e++)null!==this.subnode[e]&&(t+=this.subnode[e].nodeSize());return t+1},add:function(t){this.items.add(t)},interfaces_:function(){return[]},getClass:function(){return Zr}}),Zr.getSubnodeIndex=function(t,e){var n=-1;return t.min>=e&&(n=1),t.max<=e&&(n=0),n},e(Qr.prototype,{expandToInclude:function(t){t.max>this.max&&(this.max=t.max),t.min<this.min&&(this.min=t.min)},getWidth:function(){return this.max-this.min},overlaps:function(){if(1===arguments.length){var t=arguments[0];return this.overlaps(t.min,t.max)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return!(this.min>n||this.max<e)}},getMin:function(){return this.min},toString:function(){return"["+this.min+", "+this.max+"]"},contains:function(){if(1===arguments.length){if(arguments[0]instanceof Qr){var t=arguments[0];return this.contains(t.min,t.max)}if("number"==typeof arguments[0]){var e=arguments[0];return e>=this.min&&e<=this.max}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];return n>=this.min&&i<=this.max}},init:function(t,e){this.min=t,this.max=e,t>e&&(this.min=e,this.max=t)},getMax:function(){return this.max},interfaces_:function(){return[]},getClass:function(){return Qr}}),e(Jr.prototype,{getInterval:function(){return this.interval},getLevel:function(){return this.level},computeKey:function(t){for(this.level=Jr.computeLevel(t),this.interval=new Qr,this.computeInterval(this.level,t);!this.interval.contains(t);)this.level+=1,this.computeInterval(this.level,t)},computeInterval:function(t,e){var n=Ci.powerOf2(t);this.pt=Math.floor(e.getMin()/n)*n,this.interval.init(this.pt,this.pt+n)},getPoint:function(){return this.pt},interfaces_:function(){return[]},getClass:function(){return Jr}}),Jr.computeLevel=function(t){var e=t.getWidth(),n=Ci.exponent(e)+1;return n},h($r,Zr),e($r.prototype,{getInterval:function(){return this.interval},find:function(t){var e=Zr.getSubnodeIndex(t,this.centre);if(-1===e)return this;if(null!==this.subnode[e]){var n=this.subnode[e];return n.find(t)}return this},insert:function(t){f.isTrue(null===this.interval||this.interval.contains(t.interval));var e=Zr.getSubnodeIndex(t.interval,this.centre);if(t.level===this.level-1)this.subnode[e]=t;else{var n=this.createSubnode(e);n.insert(t),this.subnode[e]=n}},isSearchMatch:function(t){return t.overlaps(this.interval)},getSubnode:function(t){return null===this.subnode[t]&&(this.subnode[t]=this.createSubnode(t)),this.subnode[t]},getNode:function(t){var e=Zr.getSubnodeIndex(t,this.centre);if(-1!==e){var n=this.getSubnode(e);return n.getNode(t)}return this},createSubnode:function(t){var e=0,n=0;switch(t){case 0:e=this.interval.getMin(),n=this.centre;break;case 1:e=this.centre,n=this.interval.getMax()}var i=new Qr(e,n),r=new $r(i,this.level-1);return r},interfaces_:function(){return[]},getClass:function(){return $r}}),$r.createNode=function(t){var e=new Jr(t),n=new $r(e.getInterval(),e.getLevel());return n},$r.createExpanded=function(t,e){var n=new Qr(e);null!==t&&n.expandToInclude(t.interval);var i=$r.createNode(n);return null!==t&&i.insert(t),i},h(ts,Zr),e(ts.prototype,{insert:function(t,e){var n=Zr.getSubnodeIndex(t,ts.origin);if(-1===n)return this.add(e),null;var i=this.subnode[n];if(null===i||!i.getInterval().contains(t)){var r=$r.createExpanded(i,t);this.subnode[n]=r}this.insertContained(this.subnode[n],t,e)},isSearchMatch:function(t){return!0},insertContained:function(t,e,n){f.isTrue(t.getInterval().contains(e));var i=Ri.isZeroWidth(e.getMin(),e.getMax()),r=null;r=i?t.find(e):t.getNode(e),r.add(n)},interfaces_:function(){return[]},getClass:function(){return ts}}),ts.origin=0,e(es.prototype,{size:function(){return null!==this.root?this.root.size():0},insert:function(t,e){this.collectStats(t);var n=es.ensureExtent(t,this.minExtent);this.root.insert(n,e)},query:function(){if(1===arguments.length){if("number"==typeof arguments[0]){var t=arguments[0];return this.query(new Qr(t,t))}if(arguments[0]instanceof Qr){var e=arguments[0],n=new I;return this.query(e,n),n}}else if(2===arguments.length){var i=arguments[0],r=arguments[1];this.root.addAllItemsFromOverlapping(i,r)}},iterator:function(){var t=new I;return this.root.addAllItems(t),t.iterator()},remove:function(t,e){var n=es.ensureExtent(t,this.minExtent);return this.root.remove(n,e)},collectStats:function(t){var e=t.getWidth();e<this.minExtent&&e>0&&(this.minExtent=e)},depth:function(){return null!==this.root?this.root.depth():0},nodeSize:function(){return null!==this.root?this.root.nodeSize():0},interfaces_:function(){return[]},getClass:function(){return es}}),es.ensureExtent=function(t,e){var n=t.getMin(),i=t.getMax();return n!==i?t:(n===i&&(n-=e/2,i=n+e/2),new Qr(n,i))},e(ns.prototype,{isInside:function(t){},interfaces_:function(){return[]},getClass:function(){return ns}}),e(is.prototype,{testLineSegment:function(t,e){var n=null,i=null,r=null,s=null,o=null,a=e.p0,u=e.p1;i=a.x-t.x,r=a.y-t.y,s=u.x-t.x,o=u.y-t.y,(r>0&&0>=o||o>0&&0>=r)&&(n=ue.signOfDet2x2(i,r,s,o)/(o-r),n>0&&this.crossings++)},buildIndex:function(){this.tree=new es;for(var t=H.removeRepeatedPoints(this.ring.getCoordinates()),e=$e.getChains(t),n=0;n<e.size();n++){var i=e.get(n),r=i.getEnvelope();this.interval.min=r.getMinY(),this.interval.max=r.getMaxY(),this.tree.insert(this.interval,i)}},testMonotoneChain:function(t,e,n){n.select(t,e)},isInside:function(t){this.crossings=0;var e=new C(r.NEGATIVE_INFINITY,r.POSITIVE_INFINITY,t.y,t.y);this.interval.min=t.y,this.interval.max=t.y;for(var n=this.tree.query(this.interval),i=new rs(this,t),s=n.iterator();s.hasNext();){var o=s.next();this.testMonotoneChain(e,i,o)}return this.crossings%2===1},interfaces_:function(){return[ns]},getClass:function(){return is}}),h(rs,tr),e(rs.prototype,{select:function(){if(1!==arguments.length)return tr.prototype.select.apply(this,arguments);var t=arguments[0];this.mcp.testLineSegment(this.p,t)},interfaces_:function(){return[]},getClass:function(){return rs}}),is.MCSelecter=rs,e(ss.prototype,{insertEdgeEnds:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.nodes.add(n)}},getNodeIterator:function(){return this.nodes.iterator()},copyNodesAndLabels:function(t,e){for(var n=t.getNodeIterator();n.hasNext();){var i=n.next(),r=this.nodes.addNode(i.getCoordinate());r.setLabel(e,i.getLabel().getLocation(e))}},build:function(t){this.computeIntersectionNodes(t,0),this.copyNodesAndLabels(t,0);var e=new br,n=e.computeEdgeEnds(t.getEdgeIterator());this.insertEdgeEnds(n)},computeIntersectionNodes:function(t,e){for(var n=t.getEdgeIterator();n.hasNext();)for(var i=n.next(),r=i.getLabel().getLocation(e),s=i.getEdgeIntersectionList().iterator();s.hasNext();){var o=s.next(),a=this.nodes.addNode(o.coord);r===L.BOUNDARY?a.setLabelBoundary(e):a.getLabel().isNull(e)&&a.setLabel(e,L.INTERIOR)}},interfaces_:function(){return[]},getClass:function(){return ss}}),e(os.prototype,{isNodeEdgeAreaLabelsConsistent:function(){for(var t=this.nodeGraph.getNodeIterator();t.hasNext();){var e=t.next();if(!e.getEdges().isAreaLabelsConsistent(this.geomGraph))return this.invalidPoint=e.getCoordinate().copy(),!1}return!0},getInvalidPoint:function(){return this.invalidPoint},hasDuplicateRings:function(){for(var t=this.nodeGraph.getNodeIterator();t.hasNext();)for(var e=t.next(),n=e.getEdges().iterator();n.hasNext();){var i=n.next();if(i.getEdgeEnds().size()>1)return this.invalidPoint=i.getEdge().getCoordinate(0),!0}return!1},isNodeConsistentArea:function(){var t=this.geomGraph.computeSelfNodes(this.li,!0,!0);return t.hasProperIntersection()?(this.invalidPoint=t.getProperIntersectionPoint(),!1):(this.nodeGraph.build(this.geomGraph),this.isNodeEdgeAreaLabelsConsistent())},interfaces_:function(){return[]},getClass:function(){return os}}),e(as.prototype,{buildIndex:function(){this.index=new ke;for(var t=0;t<this.rings.size();t++){var e=this.rings.get(t),n=e.getEnvelopeInternal();this.index.insert(n,e)}},getNestedPoint:function(){return this.nestedPt},isNonNested:function(){this.buildIndex();for(var t=0;t<this.rings.size();t++)for(var e=this.rings.get(t),n=e.getCoordinates(),i=this.index.query(e.getEnvelopeInternal()),r=0;r<i.size();r++){var s=i.get(r),o=s.getCoordinates();if(e!==s&&e.getEnvelopeInternal().intersects(s.getEnvelopeInternal())){var a=ls.findPtNotNode(n,s,this.graph);if(null!==a){var u=he.isPointInRing(a,o);if(u)return this.nestedPt=a,!1}}}return!0},add:function(t){this.rings.add(t),this.totalEnv.expandToInclude(t.getEnvelopeInternal())},interfaces_:function(){return[]},getClass:function(){return as}}),e(us.prototype,{getErrorType:function(){return this.errorType},getMessage:function(){return us.errMsg[this.errorType]},getCoordinate:function(){return this.pt},toString:function(){var t="";return null!==this.pt&&(t=" at or near point "+this.pt),this.getMessage()+t},interfaces_:function(){return[]},getClass:function(){return us}}),us.ERROR=0,us.REPEATED_POINT=1,us.HOLE_OUTSIDE_SHELL=2,us.NESTED_HOLES=3,us.DISCONNECTED_INTERIOR=4,us.SELF_INTERSECTION=5,us.RING_SELF_INTERSECTION=6,us.NESTED_SHELLS=7,us.DUPLICATE_RINGS=8,us.TOO_FEW_POINTS=9,us.INVALID_COORDINATE=10,us.RING_NOT_CLOSED=11,us.errMsg=["Topology Validation Error","Repeated Point","Hole lies outside shell","Holes are nested","Interior is disconnected","Self-intersection","Ring Self-intersection","Nested shells","Duplicate Rings","Too few distinct points in geometry component","Invalid Coordinate","Ring is not closed"],e(ls.prototype,{checkInvalidCoordinates:function(){if(arguments[0]instanceof Array){for(var t=arguments[0],e=0;e<t.length;e++)if(!ls.isValid(t[e]))return this.validErr=new us(us.INVALID_COORDINATE,t[e]),null}else if(arguments[0]instanceof Tt){var n=arguments[0];if(this.checkInvalidCoordinates(n.getExteriorRing().getCoordinates()),null!==this.validErr)return null;for(var e=0;e<n.getNumInteriorRing();e++)if(this.checkInvalidCoordinates(n.getInteriorRingN(e).getCoordinates()),null!==this.validErr)return null}},checkHolesNotNested:function(t,e){for(var n=new as(e),i=0;i<t.getNumInteriorRing();i++){var r=t.getInteriorRingN(i);n.add(r)}var s=n.isNonNested();s||(this.validErr=new us(us.NESTED_HOLES,n.getNestedPoint()))},checkConsistentArea:function(t){var e=new os(t),n=e.isNodeConsistentArea();return n?void(e.hasDuplicateRings()&&(this.validErr=new us(us.DUPLICATE_RINGS,e.getInvalidPoint()))):(this.validErr=new us(us.SELF_INTERSECTION,e.getInvalidPoint()),null)},isValid:function(){return this.checkValid(this.parentGeometry),null===this.validErr},checkShellInsideHole:function(t,e,n){var i=t.getCoordinates(),r=e.getCoordinates(),s=ls.findPtNotNode(i,e,n);if(null!==s){var o=he.isPointInRing(s,r);if(!o)return s}var a=ls.findPtNotNode(r,t,n);if(null!==a){var u=he.isPointInRing(a,i);return u?a:null}return f.shouldNeverReachHere("points in shell and hole appear to be equal"),null},checkNoSelfIntersectingRings:function(t){for(var e=t.getEdgeIterator();e.hasNext();){var n=e.next();if(this.checkNoSelfIntersectingRing(n.getEdgeIntersectionList()),null!==this.validErr)return null}},checkConnectedInteriors:function(t){var e=new Kr(t);e.isInteriorsConnected()||(this.validErr=new us(us.DISCONNECTED_INTERIOR,e.getCoordinate()))},checkNoSelfIntersectingRing:function(t){for(var e=new at,n=!0,i=t.iterator();i.hasNext();){var r=i.next();if(n)n=!1;else{if(e.contains(r.coord))return this.validErr=new us(us.RING_SELF_INTERSECTION,r.coord),null;e.add(r.coord)}}},checkHolesInShell:function(t,e){for(var n=t.getExteriorRing(),i=new is(n),r=0;r<t.getNumInteriorRing();r++){var s=t.getInteriorRingN(r),o=ls.findPtNotNode(s.getCoordinates(),n,e);if(null===o)return null;var a=!i.isInside(o);if(a)return this.validErr=new us(us.HOLE_OUTSIDE_SHELL,o),null}},checkTooFewPoints:function(t){return t.hasTooFewPoints()?(this.validErr=new us(us.TOO_FEW_POINTS,t.getInvalidPoint()),null):void 0},getValidationError:function(){return this.checkValid(this.parentGeometry),this.validErr},checkValid:function(){if(arguments[0]instanceof Lt){var t=arguments[0];this.checkInvalidCoordinates(t.getCoordinates())}else if(arguments[0]instanceof Pt){var e=arguments[0];this.checkInvalidCoordinates(e.getCoordinates())}else if(arguments[0]instanceof bt){var n=arguments[0];if(this.checkInvalidCoordinates(n.getCoordinates()),null!==this.validErr)return null;if(this.checkClosedRing(n),null!==this.validErr)return null;var i=new $n(0,n);if(this.checkTooFewPoints(i),null!==this.validErr)return null;var r=new ae;i.computeSelfNodes(r,!0,!0),this.checkNoSelfIntersectingRings(i)}else if(arguments[0]instanceof St){var s=arguments[0];if(this.checkInvalidCoordinates(s.getCoordinates()),null!==this.validErr)return null;var i=new $n(0,s);this.checkTooFewPoints(i)}else if(arguments[0]instanceof Tt){var o=arguments[0];if(this.checkInvalidCoordinates(o),null!==this.validErr)return null;if(this.checkClosedRings(o),null!==this.validErr)return null;var i=new $n(0,o);if(this.checkTooFewPoints(i),null!==this.validErr)return null;if(this.checkConsistentArea(i),null!==this.validErr)return null;if(!this.isSelfTouchingRingFormingHoleValid&&(this.checkNoSelfIntersectingRings(i),null!==this.validErr))return null;if(this.checkHolesInShell(o,i),null!==this.validErr)return null;if(this.checkHolesNotNested(o,i),null!==this.validErr)return null;this.checkConnectedInteriors(i)}else if(arguments[0]instanceof Ot){for(var a=arguments[0],u=0;u<a.getNumGeometries();u++){var l=a.getGeometryN(u);if(this.checkInvalidCoordinates(l),null!==this.validErr)return null;if(this.checkClosedRings(l),null!==this.validErr)return null}var i=new $n(0,a);if(this.checkTooFewPoints(i),null!==this.validErr)return null;if(this.checkConsistentArea(i),null!==this.validErr)return null;if(!this.isSelfTouchingRingFormingHoleValid&&(this.checkNoSelfIntersectingRings(i),null!==this.validErr))return null;for(var u=0;u<a.getNumGeometries();u++){var l=a.getGeometryN(u);if(this.checkHolesInShell(l,i),null!==this.validErr)return null}for(var u=0;u<a.getNumGeometries();u++){var l=a.getGeometryN(u);if(this.checkHolesNotNested(l,i),null!==this.validErr)return null}if(this.checkShellsNotNested(a,i),null!==this.validErr)return null;this.checkConnectedInteriors(i)}else if(arguments[0]instanceof ft)for(var h=arguments[0],u=0;u<h.getNumGeometries();u++){var c=h.getGeometryN(u);if(this.checkValid(c),null!==this.validErr)return null}else if(arguments[0]instanceof B){var f=arguments[0];if(this.validErr=null,f.isEmpty())return null;if(f instanceof Lt)this.checkValid(f);else if(f instanceof Pt)this.checkValid(f);else if(f instanceof bt)this.checkValid(f);else if(f instanceof St)this.checkValid(f);else if(f instanceof Tt)this.checkValid(f);else if(f instanceof Ot)this.checkValid(f);else{if(!(f instanceof ft))throw new UnsupportedOperationException(f.getClass().getName());this.checkValid(f)}}},setSelfTouchingRingFormingHoleValid:function(t){this.isSelfTouchingRingFormingHoleValid=t},checkShellNotNested:function(t,e,n){var i=t.getCoordinates(),r=e.getExteriorRing(),s=r.getCoordinates(),o=ls.findPtNotNode(i,r,n);if(null===o)return null;var a=he.isPointInRing(o,s);if(!a)return null;if(e.getNumInteriorRing()<=0)return this.validErr=new us(us.NESTED_SHELLS,o),null;for(var u=null,l=0;l<e.getNumInteriorRing();l++){var h=e.getInteriorRingN(l);if(u=this.checkShellInsideHole(t,h,n),null===u)return null}this.validErr=new us(us.NESTED_SHELLS,u)},checkClosedRings:function(t){if(this.checkClosedRing(t.getExteriorRing()),null!==this.validErr)return null;for(var e=0;e<t.getNumInteriorRing();e++)if(this.checkClosedRing(t.getInteriorRingN(e)),null!==this.validErr)return null},checkClosedRing:function(t){if(!t.isClosed()){var e=null;t.getNumPoints()>=1&&(e=t.getCoordinateN(0)),this.validErr=new us(us.RING_NOT_CLOSED,e)}},checkShellsNotNested:function(t,e){for(var n=0;n<t.getNumGeometries();n++)for(var i=t.getGeometryN(n),r=i.getExteriorRing(),s=0;s<t.getNumGeometries();s++)if(n!==s){var o=t.getGeometryN(s);if(this.checkShellNotNested(r,o,e),null!==this.validErr)return null}},interfaces_:function(){return[]},getClass:function(){return ls}}),ls.findPtNotNode=function(t,e,n){for(var i=n.findEdge(e),r=i.getEdgeIntersectionList(),s=0;s<t.length;s++){var o=t[s];if(!r.isIntersection(o))return o}return null},ls.isValid=function(){if(arguments[0]instanceof B){var t=arguments[0],e=new ls(t);return e.isValid()}if(arguments[0]instanceof g){var n=arguments[0];return r.isNaN(n.x)?!1:r.isInfinite(n.x)?!1:r.isNaN(n.y)?!1:!r.isInfinite(n.y)}};var bo=Object.freeze({IsValidOp:ls}),Oo=Object.freeze({BoundaryOp:dt,IsSimpleOp:Gi,buffer:Co,distance:So,linemerge:wo,overlay:Lo,polygonize:Ro,relate:To,union:Po,valid:bo});h(hs,_t.CoordinateOperation),e(hs.prototype,{editCoordinates:function(t,e){if(0===t.length)return null;for(var n=new Array(t.length).fill(null),i=0;i<t.length;i++){var r=new g(t[i]);this.targetPM.makePrecise(r),n[i]=r}var s=new N(n,!1),o=s.toCoordinateArray(),a=0;e instanceof St&&(a=2),e instanceof bt&&(a=4);var u=n;return this.removeCollapsed&&(u=null),o.length<a?u:o},interfaces_:function(){return[]},getClass:function(){return hs}}),e(cs.prototype,{fixPolygonalTopology:function(t){var e=t;this.changePrecisionModel||(e=this.changePM(t,this.targetPM));var n=e.buffer(0),i=n;return this.changePrecisionModel||(i=t.getFactory().createGeometry(n)),i},reducePointwise:function(t){var e=null;if(this.changePrecisionModel){var n=this.createFactory(t.getFactory(),this.targetPM);e=new _t(n)}else e=new _t;var i=this.removeCollapsed;t.getDimension()>=2&&(i=!0);var r=e.edit(t,new hs(this.targetPM,i));return r},changePM:function(t,e){var n=this.createEditor(t.getFactory(),e);return n.edit(t,new _t.NoOpGeometryOperation)},setRemoveCollapsedComponents:function(t){this.removeCollapsed=t},createFactory:function(t,e){var n=new ie(e,t.getSRID(),t.getCoordinateSequenceFactory());return n},setChangePrecisionModel:function(t){this.changePrecisionModel=t},reduce:function(t){var e=this.reducePointwise(t);return this.isPointwise?e:R(e,Rt)?e.isValid()?e:this.fixPolygonalTopology(e):e},setPointwise:function(t){this.isPointwise=t},createEditor:function(t,e){if(t.getPrecisionModel()===e)return new _t;var n=this.createFactory(t,e),i=new _t(n);return i},interfaces_:function(){return[]},getClass:function(){return cs}}),cs.reduce=function(t,e){var n=new cs(e);return n.reduce(t)},cs.reducePointwise=function(t,e){var n=new cs(e);return n.setPointwise(!0),n.reduce(t)};var _o=Object.freeze({GeometryPrecisionReducer:cs});e(fs.prototype,{simplifySection:function(t,e){if(t+1===e)return null;this.seg.p0=this.pts[t],this.seg.p1=this.pts[e];for(var n=-1,i=t,r=t+1;e>r;r++){var s=this.seg.distance(this.pts[r]);s>n&&(n=s,i=r)}if(n<=this.distanceTolerance)for(var r=t+1;e>r;r++)this.usePt[r]=!1;else this.simplifySection(t,i),this.simplifySection(i,e)},setDistanceTolerance:function(t){this.distanceTolerance=t},simplify:function(){this.usePt=new Array(this.pts.length).fill(null);for(var t=0;t<this.pts.length;t++)this.usePt[t]=!0;this.simplifySection(0,this.pts.length-1);for(var e=new N,t=0;t<this.pts.length;t++)this.usePt[t]&&e.add(new g(this.pts[t]));return e.toCoordinateArray()},interfaces_:function(){return[]},getClass:function(){return fs}}),fs.simplify=function(t,e){var n=new fs(t);return n.setDistanceTolerance(e),n.simplify()},e(gs.prototype,{setEnsureValid:function(t){this.isEnsureValidTopology=t},getResultGeometry:function(){return this.inputGeom.isEmpty()?this.inputGeom.copy():new ds(this.isEnsureValidTopology,this.distanceTolerance).transform(this.inputGeom)},setDistanceTolerance:function(t){if(0>t)throw new i("Tolerance must be non-negative");this.distanceTolerance=t},interfaces_:function(){return[]},getClass:function(){return gs}}),gs.simplify=function(t,e){var n=new gs(t);return n.setDistanceTolerance(e),n.getResultGeometry()},h(ds,xe),e(ds.prototype,{transformPolygon:function(t,e){if(t.isEmpty())return null;var n=xe.prototype.transformPolygon.call(this,t,e);return e instanceof Ot?n:this.createValidArea(n)},createValidArea:function(t){return this.isEnsureValidTopology?t.buffer(0):t},transformCoordinates:function(t,e){var n=t.toCoordinateArray(),i=null;return i=0===n.length?new Array(0).fill(null):fs.simplify(n,this.distanceTolerance),this.factory.getCoordinateSequenceFactory().create(i)},transformMultiPolygon:function(t,e){var n=xe.prototype.transformMultiPolygon.call(this,t,e);return this.createValidArea(n)},transformLinearRing:function(t,e){var n=e instanceof Tt,i=xe.prototype.transformLinearRing.call(this,t,e);return!n||i instanceof bt?i:null},interfaces_:function(){return[]},getClass:function(){return ds}}),gs.DPTransformer=ds,h(ps,ce),e(ps.prototype,{getIndex:function(){return this.index},getParent:function(){return this.parent},interfaces_:function(){return[]},getClass:function(){return ps}}),e(vs.prototype,{addToResult:function(t){this.resultSegs.add(t)},asLineString:function(){return this.parentLine.getFactory().createLineString(vs.extractCoordinates(this.resultSegs))},getResultSize:function(){var t=this.resultSegs.size();return 0===t?0:t+1},getParent:function(){return this.parentLine},getSegment:function(t){return this.segs[t]},getParentCoordinates:function(){return this.parentLine.getCoordinates()},getMinimumSize:function(){return this.minimumSize},asLinearRing:function(){return this.parentLine.getFactory().createLinearRing(vs.extractCoordinates(this.resultSegs))},getSegments:function(){return this.segs},init:function(){var t=this.parentLine.getCoordinates();this.segs=new Array(t.length-1).fill(null);for(var e=0;e<t.length-1;e++){var n=new ps(t[e],t[e+1],this.parentLine,e);this.segs[e]=n}},getResultCoordinates:function(){return vs.extractCoordinates(this.resultSegs)},interfaces_:function(){return[]},getClass:function(){return vs}}),vs.extractCoordinates=function(t){for(var e=new Array(t.size()+1).fill(null),n=null,i=0;i<t.size();i++)n=t.get(i),e[i]=n.p0;return e[e.length-1]=n.p1,e},e(ms.prototype,{remove:function(t){this.index.remove(new C(t.p0,t.p1),t)},add:function(){if(arguments[0]instanceof vs)for(var t=arguments[0],e=t.getSegments(),n=0;n<e.length;n++){var i=e[n];this.add(i)}else if(arguments[0]instanceof ce){var r=arguments[0];this.index.insert(new C(r.p0,r.p1),r)}},query:function(t){var e=new C(t.p0,t.p1),n=new ys(t);this.index.query(e,n);var i=n.getItems();return i},interfaces_:function(){return[]},getClass:function(){return ms}}),e(ys.prototype,{visitItem:function(t){var e=t;C.intersects(e.p0,e.p1,this.querySeg.p0,this.querySeg.p1)&&this.items.add(t)},getItems:function(){return this.items},interfaces_:function(){return[Ae]},getClass:function(){return ys}}),e(xs.prototype,{flatten:function(t,e){var n=this.linePts[t],i=this.linePts[e],r=new ce(n,i);return this.remove(this.line,t,e),this.outputIndex.add(r),r},hasBadIntersection:function(t,e,n){return this.hasBadOutputIntersection(n)?!0:!!this.hasBadInputIntersection(t,e,n)},setDistanceTolerance:function(t){this.distanceTolerance=t},simplifySection:function(t,e,n){n+=1;var i=new Array(2).fill(null);if(t+1===e){var r=this.line.getSegment(t);return this.line.addToResult(r),null}var s=!0;if(this.line.getResultSize()<this.line.getMinimumSize()){var o=n+1;o<this.line.getMinimumSize()&&(s=!1)}var a=new Array(1).fill(null),u=this.findFurthestPoint(this.linePts,t,e,a);a[0]>this.distanceTolerance&&(s=!1);var l=new ce;if(l.p0=this.linePts[t],l.p1=this.linePts[e],i[0]=t,i[1]=e,this.hasBadIntersection(this.line,i,l)&&(s=!1),s){var r=this.flatten(t,e);return this.line.addToResult(r),null}this.simplifySection(t,u,n),this.simplifySection(u,e,n)},hasBadOutputIntersection:function(t){for(var e=this.outputIndex.query(t),n=e.iterator();n.hasNext();){var i=n.next();if(this.hasInteriorIntersection(i,t))return!0}return!1},findFurthestPoint:function(t,e,n,i){var r=new ce;r.p0=t[e],r.p1=t[n];for(var s=-1,o=e,a=e+1;n>a;a++){var u=t[a],l=r.distance(u);l>s&&(s=l,o=a)}return i[0]=s,o},simplify:function(t){this.line=t,this.linePts=t.getParentCoordinates(),this.simplifySection(0,this.linePts.length-1,0)},remove:function(t,e,n){for(var i=e;n>i;i++){var r=t.getSegment(i);this.inputIndex.remove(r)}},hasInteriorIntersection:function(t,e){return this.li.computeIntersection(t.p0,t.p1,e.p0,e.p1),this.li.isInteriorIntersection()},hasBadInputIntersection:function(t,e,n){for(var i=this.inputIndex.query(n),r=i.iterator();r.hasNext();){var s=r.next();if(this.hasInteriorIntersection(s,n)){if(xs.isInLineSection(t,e,s))continue;return!0}}return!1},interfaces_:function(){return[]},getClass:function(){return xs}}),xs.isInLineSection=function(t,e,n){if(n.getParent()!==t.getParent())return!1;var i=n.getIndex();return i>=e[0]&&i<e[1]},e(Es.prototype,{setDistanceTolerance:function(t){this.distanceTolerance=t},simplify:function(t){for(var e=t.iterator();e.hasNext();)this.inputIndex.add(e.next());for(var e=t.iterator();e.hasNext();){var n=new xs(this.inputIndex,this.outputIndex);n.setDistanceTolerance(this.distanceTolerance),n.simplify(e.next())}},interfaces_:function(){return[]},getClass:function(){return Es}}),e(Is.prototype,{getResultGeometry:function(){if(this.inputGeom.isEmpty())return this.inputGeom.copy();this.linestringMap=new te,this.inputGeom.apply(new Cs(this)),this.lineSimplifier.simplify(this.linestringMap.values());var t=new Ns(this.linestringMap).transform(this.inputGeom);return t},setDistanceTolerance:function(t){if(0>t)throw new i("Tolerance must be non-negative");this.lineSimplifier.setDistanceTolerance(t)},interfaces_:function(){return[]},getClass:function(){return Is}}),Is.simplify=function(t,e){var n=new Is(t);return n.setDistanceTolerance(e),n.getResultGeometry()},h(Ns,xe),e(Ns.prototype,{transformCoordinates:function(t,e){if(0===t.size())return null;if(e instanceof St){var n=this.linestringMap.get(e);return this.createCoordinateSequence(n.getResultCoordinates())}return xe.prototype.transformCoordinates.call(this,t,e)},interfaces_:function(){return[]},getClass:function(){return Ns}}),e(Cs.prototype,{filter:function(t){if(t instanceof St){var e=t;if(e.isEmpty())return null;var n=e.isClosed()?4:2,i=new vs(e,n);this.tps.linestringMap.put(e,i)}},interfaces_:function(){return[q]},getClass:function(){return Cs}}),Is.LineStringTransformer=Ns,Is.LineStringMapBuilderFilter=Cs;var Mo=Object.freeze({DouglasPeuckerSimplifier:gs,TopologyPreservingSimplifier:Is});e(Ss.prototype,{splitAt:function(){if(1===arguments.length){var t=arguments[0],e=this.minimumLen/this.segLen;if(t.distance(this.seg.p0)<this.minimumLen)return this.splitPt=this.seg.pointAlong(e),null;if(t.distance(this.seg.p1)<this.minimumLen)return this.splitPt=Ss.pointAlongReverse(this.seg,e),null;this.splitPt=t}else if(2===arguments.length){var n=arguments[0],i=arguments[1],r=this.getConstrainedLength(n),s=r/this.segLen;i.equals2D(this.seg.p0)?this.splitPt=this.seg.pointAlong(s):this.splitPt=Ss.pointAlongReverse(this.seg,s)}},setMinimumLength:function(t){this.minimumLen=t},getConstrainedLength:function(t){return t<this.minimumLen?this.minimumLen:t},getSplitPoint:function(){return this.splitPt},interfaces_:function(){return[]},getClass:function(){return Ss}}),Ss.pointAlongReverse=function(t,e){var n=new g;return n.x=t.p1.x-e*(t.p1.x-t.p0.x),n.y=t.p1.y-e*(t.p1.y-t.p0.y),n},e(ws.prototype,{findSplitPoint:function(t,e){},interfaces_:function(){return[]},getClass:function(){return ws}}),e(Ls.prototype,{findSplitPoint:function(t,e){var n=t.getLineSegment(),i=n.getLength(),r=i/2,s=new Ss(n),o=Ls.projectedSplitPoint(t,e),a=2*o.distance(e)*.8,u=a;return u>r&&(u=r),s.setMinimumLength(u),s.splitAt(o),s.getSplitPoint()},interfaces_:function(){return[ws]},getClass:function(){return Ls}}),Ls.projectedSplitPoint=function(t,e){var n=t.getLineSegment(),i=n.project(e);return i},e(Rs.prototype,{interfaces_:function(){return[]},getClass:function(){return Rs}}),Rs.triArea=function(t,e,n){return(e.x-t.x)*(n.y-t.y)-(e.y-t.y)*(n.x-t.x)},Rs.isInCircleDDNormalized=function(t,e,n,i){var r=_.valueOf(t.x).selfSubtract(i.x),s=_.valueOf(t.y).selfSubtract(i.y),o=_.valueOf(e.x).selfSubtract(i.x),a=_.valueOf(e.y).selfSubtract(i.y),u=_.valueOf(n.x).selfSubtract(i.x),l=_.valueOf(n.y).selfSubtract(i.y),h=r.multiply(a).selfSubtract(o.multiply(s)),c=o.multiply(l).selfSubtract(u.multiply(a)),f=u.multiply(s).selfSubtract(r.multiply(l)),g=r.multiply(r).selfAdd(s.multiply(s)),d=o.multiply(o).selfAdd(a.multiply(a)),p=u.multiply(u).selfAdd(l.multiply(l)),v=g.selfMultiply(c).selfAdd(d.selfMultiply(f)).selfAdd(p.selfMultiply(h)),m=v.doubleValue()>0;return m},Rs.checkRobustInCircle=function(t,e,n,i){var r=Rs.isInCircleNonRobust(t,e,n,i),s=Rs.isInCircleDDSlow(t,e,n,i),o=Rs.isInCircleCC(t,e,n,i),a=ci.circumcentre(t,e,n);A.out.println("p radius diff a = "+Math.abs(i.distance(a)-t.distance(a))/t.distance(a)),r===s&&r===o||(A.out.println("inCircle robustness failure (double result = "+r+", DD result = "+s+", CC result = "+o+")"),A.out.println(se.toLineString(new Gt([t,e,n,i]))),A.out.println("Circumcentre = "+se.toPoint(a)+" radius = "+t.distance(a)),A.out.println("p radius diff a = "+Math.abs(i.distance(a)/t.distance(a)-1)),A.out.println("p radius diff b = "+Math.abs(i.distance(a)/e.distance(a)-1)),A.out.println("p radius diff c = "+Math.abs(i.distance(a)/n.distance(a)-1)),A.out.println())},Rs.isInCircleDDFast=function(t,e,n,i){var r=_.sqr(t.x).selfAdd(_.sqr(t.y)).selfMultiply(Rs.triAreaDDFast(e,n,i)),s=_.sqr(e.x).selfAdd(_.sqr(e.y)).selfMultiply(Rs.triAreaDDFast(t,n,i)),o=_.sqr(n.x).selfAdd(_.sqr(n.y)).selfMultiply(Rs.triAreaDDFast(t,e,i)),a=_.sqr(i.x).selfAdd(_.sqr(i.y)).selfMultiply(Rs.triAreaDDFast(t,e,n)),u=r.selfSubtract(s).selfAdd(o).selfSubtract(a),l=u.doubleValue()>0;return l},Rs.isInCircleCC=function(t,e,n,i){var r=ci.circumcentre(t,e,n),s=t.distance(r),o=i.distance(r)-s;return 0>=o},Rs.isInCircleNormalized=function(t,e,n,i){var r=t.x-i.x,s=t.y-i.y,o=e.x-i.x,a=e.y-i.y,u=n.x-i.x,l=n.y-i.y,h=r*a-o*s,c=o*l-u*a,f=u*s-r*l,g=r*r+s*s,d=o*o+a*a,p=u*u+l*l,v=g*c+d*f+p*h;return v>0},Rs.isInCircleDDSlow=function(t,e,n,i){var r=_.valueOf(i.x),s=_.valueOf(i.y),o=_.valueOf(t.x),a=_.valueOf(t.y),u=_.valueOf(e.x),l=_.valueOf(e.y),h=_.valueOf(n.x),c=_.valueOf(n.y),f=o.multiply(o).add(a.multiply(a)).multiply(Rs.triAreaDDSlow(u,l,h,c,r,s)),g=u.multiply(u).add(l.multiply(l)).multiply(Rs.triAreaDDSlow(o,a,h,c,r,s)),d=h.multiply(h).add(c.multiply(c)).multiply(Rs.triAreaDDSlow(o,a,u,l,r,s)),p=r.multiply(r).add(s.multiply(s)).multiply(Rs.triAreaDDSlow(o,a,u,l,h,c)),v=f.subtract(g).add(d).subtract(p),m=v.doubleValue()>0;
return m},Rs.isInCircleNonRobust=function(t,e,n,i){var r=(t.x*t.x+t.y*t.y)*Rs.triArea(e,n,i)-(e.x*e.x+e.y*e.y)*Rs.triArea(t,n,i)+(n.x*n.x+n.y*n.y)*Rs.triArea(t,e,i)-(i.x*i.x+i.y*i.y)*Rs.triArea(t,e,n)>0;return r},Rs.isInCircleRobust=function(t,e,n,i){return Rs.isInCircleNormalized(t,e,n,i)},Rs.triAreaDDSlow=function(t,e,n,i,r,s){return n.subtract(t).multiply(s.subtract(e)).subtract(i.subtract(e).multiply(r.subtract(t)))},Rs.triAreaDDFast=function(t,e,n){var i=_.valueOf(e.x).selfSubtract(t.x).selfMultiply(_.valueOf(n.y).selfSubtract(t.y)),r=_.valueOf(e.y).selfSubtract(t.y).selfMultiply(_.valueOf(n.x).selfSubtract(t.x));return i.selfSubtract(r)},e(Ts.prototype,{circleCenter:function(t,e){var n=new Ts(this.getX(),this.getY()),i=this.bisector(n,t),r=this.bisector(t,e),s=new F(i,r),o=null;try{o=new Ts(s.getX(),s.getY())}catch(i){if(!(i instanceof w))throw i;A.err.println("a: "+n+"  b: "+t+"  c: "+e),A.err.println(i)}finally{}return o},dot:function(t){return this.p.x*t.getX()+this.p.y*t.getY()},magn:function(){return Math.sqrt(this.p.x*this.p.x+this.p.y*this.p.y)},getZ:function(){return this.p.z},bisector:function(t,e){var n=e.getX()-t.getX(),i=e.getY()-t.getY(),r=new F(t.getX()+n/2,t.getY()+i/2,1),s=new F(t.getX()-i+n/2,t.getY()+n+i/2,1);return new F(r,s)},equals:function(){if(1===arguments.length){var t=arguments[0];return this.p.x===t.getX()&&this.p.y===t.getY()}if(2===arguments.length){var e=arguments[0],n=arguments[1];return this.p.distance(e.getCoordinate())<n}},getCoordinate:function(){return this.p},isInCircle:function(t,e,n){return Rs.isInCircleRobust(t.p,e.p,n.p,this.p)},interpolateZValue:function(t,e,n){var i=t.getX(),r=t.getY(),s=e.getX()-i,o=n.getX()-i,a=e.getY()-r,u=n.getY()-r,l=s*u-o*a,h=this.getX()-i,c=this.getY()-r,f=(u*h-o*c)/l,g=(-a*h+s*c)/l,d=t.getZ()+f*(e.getZ()-t.getZ())+g*(n.getZ()-t.getZ());return d},midPoint:function(t){var e=(this.p.x+t.getX())/2,n=(this.p.y+t.getY())/2,i=(this.p.z+t.getZ())/2;return new Ts(e,n,i)},rightOf:function(t){return this.isCCW(t.dest(),t.orig())},isCCW:function(t,e){return(t.p.x-this.p.x)*(e.p.y-this.p.y)-(t.p.y-this.p.y)*(e.p.x-this.p.x)>0},getX:function(){return this.p.x},crossProduct:function(t){return this.p.x*t.getY()-this.p.y*t.getX()},setZ:function(t){this.p.z=t},times:function(t){return new Ts(t*this.p.x,t*this.p.y)},cross:function(){return new Ts(this.p.y,-this.p.x)},leftOf:function(t){return this.isCCW(t.orig(),t.dest())},toString:function(){return"POINT ("+this.p.x+" "+this.p.y+")"},sub:function(t){return new Ts(this.p.x-t.getX(),this.p.y-t.getY())},getY:function(){return this.p.y},classify:function(t,e){var n=this,i=e.sub(t),r=n.sub(t),s=i.crossProduct(r);return s>0?Ts.LEFT:0>s?Ts.RIGHT:i.getX()*r.getX()<0||i.getY()*r.getY()<0?Ts.BEHIND:i.magn()<r.magn()?Ts.BEYOND:t.equals(n)?Ts.ORIGIN:e.equals(n)?Ts.DESTINATION:Ts.BETWEEN},sum:function(t){return new Ts(this.p.x+t.getX(),this.p.y+t.getY())},distance:function(t,e){return Math.sqrt(Math.pow(e.getX()-t.getX(),2)+Math.pow(e.getY()-t.getY(),2))},circumRadiusRatio:function(t,e){var n=this.circleCenter(t,e),i=this.distance(n,t),r=this.distance(this,t),s=this.distance(t,e);return r>s&&(r=s),s=this.distance(e,this),r>s&&(r=s),i/r},interfaces_:function(){return[]},getClass:function(){return Ts}}),Ts.interpolateZ=function(){if(3===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],i=e.distance(n),r=t.distance(e),s=n.z-e.z,o=e.z+s*(r/i);return o}if(4===arguments.length){var a=arguments[0],u=arguments[1],l=arguments[2],h=arguments[3],c=u.x,f=u.y,g=l.x-c,d=h.x-c,p=l.y-f,v=h.y-f,m=g*v-d*p,y=a.x-c,x=a.y-f,E=(v*y-d*x)/m,I=(-p*y+g*x)/m,N=u.z+E*(l.z-u.z)+I*(h.z-u.z);return N}},Ts.LEFT=0,Ts.RIGHT=1,Ts.BEYOND=2,Ts.BEHIND=3,Ts.BETWEEN=4,Ts.ORIGIN=5,Ts.DESTINATION=6,h(Ps,Ts),e(Ps.prototype,{getConstraint:function(){return this.constraint},setOnConstraint:function(t){this._isOnConstraint=t},merge:function(t){t._isOnConstraint&&(this._isOnConstraint=!0,this.constraint=t.constraint)},isOnConstraint:function(){return this._isOnConstraint},setConstraint:function(t){this._isOnConstraint=!0,this.constraint=t},interfaces_:function(){return[]},getClass:function(){return Ps}}),e(bs.prototype,{equalsNonOriented:function(t){return this.equalsOriented(t)?!0:!!this.equalsOriented(t.sym())},toLineSegment:function(){return new ce(this.vertex.getCoordinate(),this.dest().getCoordinate())},dest:function(){return this.sym().orig()},oNext:function(){return this.next},equalsOriented:function(t){return!(!this.orig().getCoordinate().equals2D(t.orig().getCoordinate())||!this.dest().getCoordinate().equals2D(t.dest().getCoordinate()))},dNext:function(){return this.sym().oNext().sym()},lPrev:function(){return this.next.sym()},rPrev:function(){return this.sym().oNext()},rot:function(){return this._rot},oPrev:function(){return this._rot.next._rot},sym:function(){return this._rot._rot},setOrig:function(t){this.vertex=t},lNext:function(){return this.invRot().oNext().rot()},getLength:function(){return this.orig().getCoordinate().distance(this.dest().getCoordinate())},invRot:function(){return this._rot.sym()},setDest:function(t){this.sym().setOrig(t)},setData:function(t){this.data=t},getData:function(){return this.data},delete:function(){this._rot=null},orig:function(){return this.vertex},rNext:function(){return this._rot.next.invRot()},toString:function(){var t=this.vertex.getCoordinate(),e=this.dest().getCoordinate();return se.toLineString(t,e)},isLive:function(){return null!==this._rot},getPrimary:function(){return this.orig().getCoordinate().compareTo(this.dest().getCoordinate())<=0?this:this.sym()},dPrev:function(){return this.invRot().oNext().invRot()},setNext:function(t){this.next=t},interfaces_:function(){return[]},getClass:function(){return bs}}),bs.makeEdge=function(t,e){var n=new bs,i=new bs,r=new bs,s=new bs;n._rot=i,i._rot=r,r._rot=s,s._rot=n,n.setNext(n),i.setNext(s),r.setNext(r),s.setNext(i);var o=n;return o.setOrig(t),o.setDest(e),o},bs.swap=function(t){var e=t.oPrev(),n=t.sym().oPrev();bs.splice(t,e),bs.splice(t.sym(),n),bs.splice(t,e.lNext()),bs.splice(t.sym(),n.lNext()),t.setOrig(e.dest()),t.setDest(n.dest())},bs.splice=function(t,e){var n=t.oNext().rot(),i=e.oNext().rot(),r=e.oNext(),s=t.oNext(),o=i.oNext(),a=n.oNext();t.setNext(r),e.setNext(s),n.setNext(o),i.setNext(a)},bs.connect=function(t,e){var n=bs.makeEdge(t.dest(),e.orig());return bs.splice(n,t.lNext()),bs.splice(n.sym(),e),n},e(Os.prototype,{insertSite:function(t){var e=this.subdiv.locate(t);if(this.subdiv.isVertexOfEdge(e,t))return e;this.subdiv.isOnEdge(e,t.getCoordinate())&&(e=e.oPrev(),this.subdiv.delete(e.oNext()));var n=this.subdiv.makeEdge(e.orig(),t);bs.splice(n,e);var i=n;do n=this.subdiv.connect(e,n.sym()),e=n.oPrev();while(e.lNext()!==i);for(;;){var r=e.oPrev();if(r.dest().rightOf(e)&&t.isInCircle(e.orig(),r.dest(),e.dest()))bs.swap(e),e=e.oPrev();else{if(e.oNext()===i)return n;e=e.oNext().lPrev()}}},insertSites:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.insertSite(n)}},interfaces_:function(){return[]},getClass:function(){return Os}}),e(_s.prototype,{locate:function(t){},interfaces_:function(){return[]},getClass:function(){return _s}}),e(Ms.prototype,{init:function(){this.lastEdge=this.findEdge()},locate:function(t){this.lastEdge.isLive()||this.init();var e=this.subdiv.locateFromEdge(t,this.lastEdge);return this.lastEdge=e,e},findEdge:function(){var t=this.subdiv.getEdges();return t.iterator().next()},interfaces_:function(){return[_s]},getClass:function(){return Ms}}),h(Ds,l),e(Ds.prototype,{getSegment:function(){return this.seg},interfaces_:function(){return[]},getClass:function(){return Ds}}),Ds.msgWithSpatial=function(t,e){return null!==e?t+" [ "+e+" ]":t},e(As.prototype,{visit:function(t){},interfaces_:function(){return[]},getClass:function(){return As}}),e(Fs.prototype,{getTriangleVertices:function(t){var e=new Bs;return this.visitTriangles(e,t),e.getTriangleVertices()},isFrameVertex:function(t){return t.equals(this.frameVertex[0])?!0:t.equals(this.frameVertex[1])?!0:!!t.equals(this.frameVertex[2])},isVertexOfEdge:function(t,e){return!(!e.equals(t.orig(),this.tolerance)&&!e.equals(t.dest(),this.tolerance))},connect:function(t,e){var n=bs.connect(t,e);return this.quadEdges.add(n),n},getVoronoiCellPolygon:function(t,e){var n=new I,i=t;do{var r=t.rot().orig().getCoordinate();n.add(r),t=t.oPrev()}while(t!==i);var s=new N;s.addAll(n,!1),s.closeRing(),s.size()<4&&(A.out.println(s),s.add(s.get(s.size()-1),!0));var o=s.toCoordinateArray(),a=e.createPolygon(e.createLinearRing(o),null),u=i.orig();return a.setUserData(u.getCoordinate()),a},setLocator:function(t){this.locator=t},initSubdiv:function(){var t=this.makeEdge(this.frameVertex[0],this.frameVertex[1]),e=this.makeEdge(this.frameVertex[1],this.frameVertex[2]);bs.splice(t.sym(),e);var n=this.makeEdge(this.frameVertex[2],this.frameVertex[0]);return bs.splice(e.sym(),n),bs.splice(n.sym(),t),t},isFrameBorderEdge:function(t){var e=new Array(3).fill(null);Fs.getTriangleEdges(t,e);var n=new Array(3).fill(null);Fs.getTriangleEdges(t.sym(),n);var i=t.lNext().dest();if(this.isFrameVertex(i))return!0;var r=t.sym().lNext().dest();return!!this.isFrameVertex(r)},makeEdge:function(t,e){var n=bs.makeEdge(t,e);return this.quadEdges.add(n),n},visitTriangles:function(t,e){this.visitedKey++;var n=new pe;n.push(this.startingEdge);for(var i=new J;!n.empty();){var r=n.pop();if(!i.contains(r)){var s=this.fetchTriangleToVisit(r,n,e,i);null!==s&&t.visit(s)}}},isFrameEdge:function(t){return!(!this.isFrameVertex(t.orig())&&!this.isFrameVertex(t.dest()))},isOnEdge:function(t,e){this.seg.setCoordinates(t.orig().getCoordinate(),t.dest().getCoordinate());var n=this.seg.distance(e);return n<this.edgeCoincidenceTolerance},getEnvelope:function(){return new C(this.frameEnv)},createFrame:function(t){var e=t.getWidth(),n=t.getHeight(),i=0;i=e>n?10*e:10*n,this.frameVertex[0]=new Ts((t.getMaxX()+t.getMinX())/2,t.getMaxY()+i),this.frameVertex[1]=new Ts(t.getMinX()-i,t.getMinY()-i),this.frameVertex[2]=new Ts(t.getMaxX()+i,t.getMinY()-i),this.frameEnv=new C(this.frameVertex[0].getCoordinate(),this.frameVertex[1].getCoordinate()),this.frameEnv.expandToInclude(this.frameVertex[2].getCoordinate())},getTriangleCoordinates:function(t){var e=new zs;return this.visitTriangles(e,t),e.getTriangles()},getVertices:function(t){for(var e=new J,n=this.quadEdges.iterator();n.hasNext();){var i=n.next(),r=i.orig();!t&&this.isFrameVertex(r)||e.add(r);var s=i.dest();!t&&this.isFrameVertex(s)||e.add(s)}return e},fetchTriangleToVisit:function(t,e,n,i){var r=t,s=0,o=!1;do{this.triEdges[s]=r,this.isFrameEdge(r)&&(o=!0);var a=r.sym();i.contains(a)||e.push(a),i.add(r),s++,r=r.lNext()}while(r!==t);return o&&!n?null:this.triEdges},getEdges:function(){if(0===arguments.length)return this.quadEdges;if(1===arguments.length){for(var t=arguments[0],e=this.getPrimaryEdges(!1),n=new Array(e.size()).fill(null),i=0,r=e.iterator();r.hasNext();){var s=r.next();n[i++]=t.createLineString([s.orig().getCoordinate(),s.dest().getCoordinate()])}return t.createMultiLineString(n)}},getVertexUniqueEdges:function(t){for(var e=new I,n=new J,i=this.quadEdges.iterator();i.hasNext();){var r=i.next(),s=r.orig();n.contains(s)||(n.add(s),!t&&this.isFrameVertex(s)||e.add(r));var o=r.sym(),a=o.orig();n.contains(a)||(n.add(a),!t&&this.isFrameVertex(a)||e.add(o))}return e},getTriangleEdges:function(t){var e=new qs;return this.visitTriangles(e,t),e.getTriangleEdges()},getPrimaryEdges:function(t){this.visitedKey++;var e=new I,n=new pe;n.push(this.startingEdge);for(var i=new J;!n.empty();){var r=n.pop();if(!i.contains(r)){var s=r.getPrimary();!t&&this.isFrameEdge(s)||e.add(s),n.push(r.oNext()),n.push(r.sym().oNext()),i.add(r),i.add(r.sym())}}return e},delete:function(t){bs.splice(t,t.oPrev()),bs.splice(t.sym(),t.sym().oPrev());var e=t.sym(),n=t.rot(),i=t.rot().sym();this.quadEdges.remove(t),this.quadEdges.remove(e),this.quadEdges.remove(n),this.quadEdges.remove(i),t.delete(),e.delete(),n.delete(),i.delete()},locateFromEdge:function(t,e){for(var n=0,i=this.quadEdges.size(),r=e;;){if(n++,n>i)throw new Ds(r.toLineSegment());if(t.equals(r.orig())||t.equals(r.dest()))break;if(t.rightOf(r))r=r.sym();else if(t.rightOf(r.oNext())){if(t.rightOf(r.dPrev()))break;r=r.dPrev()}else r=r.oNext()}return r},getTolerance:function(){return this.tolerance},getVoronoiCellPolygons:function(t){this.visitTriangles(new Gs,!0);for(var e=new I,n=this.getVertexUniqueEdges(!1),i=n.iterator();i.hasNext();){var r=i.next();e.add(this.getVoronoiCellPolygon(r,t))}return e},getVoronoiDiagram:function(t){var e=this.getVoronoiCellPolygons(t);return t.createGeometryCollection(ie.toGeometryArray(e))},getTriangles:function(t){for(var e=this.getTriangleCoordinates(!1),n=new Array(e.size()).fill(null),i=0,r=e.iterator();r.hasNext();){var s=r.next();n[i++]=t.createPolygon(t.createLinearRing(s),null)}return t.createGeometryCollection(n)},insertSite:function(t){var e=this.locate(t);if(t.equals(e.orig(),this.tolerance)||t.equals(e.dest(),this.tolerance))return e;var n=this.makeEdge(e.orig(),t);bs.splice(n,e);var i=n;do n=this.connect(e,n.sym()),e=n.oPrev();while(e.lNext()!==i);return i},locate:function(){if(1===arguments.length){if(arguments[0]instanceof Ts){var t=arguments[0];return this.locator.locate(t)}if(arguments[0]instanceof g){var e=arguments[0];return this.locator.locate(new Ts(e))}}else if(2===arguments.length){var n=arguments[0],i=arguments[1],r=this.locator.locate(new Ts(n));if(null===r)return null;var s=r;r.dest().getCoordinate().equals2D(n)&&(s=r.sym());var o=s;do{if(o.dest().getCoordinate().equals2D(i))return o;o=o.oNext()}while(o!==s);return null}},interfaces_:function(){return[]},getClass:function(){return Fs}}),Fs.getTriangleEdges=function(t,e){if(e[0]=t,e[1]=e[0].lNext(),e[2]=e[1].lNext(),e[2].lNext()!==e[0])throw new i("Edges do not form a triangle")},e(Gs.prototype,{visit:function(t){for(var e=t[0].orig().getCoordinate(),n=t[1].orig().getCoordinate(),i=t[2].orig().getCoordinate(),r=ci.circumcentre(e,n,i),s=new Ts(r),o=0;3>o;o++)t[o].rot().setOrig(s)},interfaces_:function(){return[As]},getClass:function(){return Gs}}),e(qs.prototype,{getTriangleEdges:function(){return this.triList},visit:function(t){this.triList.add(t.clone())},interfaces_:function(){return[As]},getClass:function(){return qs}}),e(Bs.prototype,{visit:function(t){this.triList.add([t[0].orig(),t[1].orig(),t[2].orig()])},getTriangleVertices:function(){return this.triList},interfaces_:function(){return[As]},getClass:function(){return Bs}}),e(zs.prototype,{checkTriangleSize:function(t){var e="";t.length>=2?e=se.toLineString(t[0],t[1]):t.length>=1&&(e=se.toPoint(t[0]))},visit:function(t){this.coordList.clear();for(var e=0;3>e;e++){var n=t[e].orig();this.coordList.add(n.getCoordinate())}if(this.coordList.size()>0){this.coordList.closeRing();var i=this.coordList.toCoordinateArray();if(4!==i.length)return null;this.triCoords.add(i)}},getTriangles:function(){return this.triCoords},interfaces_:function(){return[As]},getClass:function(){return zs}}),Fs.TriangleCircumcentreVisitor=Gs,Fs.TriangleEdgesListVisitor=qs,Fs.TriangleVertexListVisitor=Bs,Fs.TriangleCoordinatesVisitor=zs,Fs.EDGE_COINCIDENCE_TOL_FACTOR=1e3,e(Vs.prototype,{getLineSegment:function(){return this.ls},getEndZ:function(){var t=this.ls.getCoordinate(1);return t.z},getStartZ:function(){var t=this.ls.getCoordinate(0);return t.z},intersection:function(t){return this.ls.intersection(t.getLineSegment())},getStart:function(){return this.ls.getCoordinate(0)},getEnd:function(){return this.ls.getCoordinate(1)},getEndY:function(){var t=this.ls.getCoordinate(1);return t.y},getStartX:function(){var t=this.ls.getCoordinate(0);return t.x},equalsTopo:function(t){return this.ls.equalsTopo(t.getLineSegment())},getStartY:function(){var t=this.ls.getCoordinate(0);return t.y},setData:function(t){this.data=t},getData:function(){return this.data},getEndX:function(){var t=this.ls.getCoordinate(1);return t.x},toString:function(){return this.ls.toString()},interfaces_:function(){return[]},getClass:function(){return Vs}}),e(ks.prototype,{visit:function(t){},interfaces_:function(){return[]},getClass:function(){return ks}}),e(Ys.prototype,{isRepeated:function(){return this.count>1},getRight:function(){return this.right},getCoordinate:function(){return this.p},setLeft:function(t){this.left=t},getX:function(){return this.p.x},getData:function(){return this.data},getCount:function(){return this.count},getLeft:function(){return this.left},getY:function(){return this.p.y},increment:function(){this.count=this.count+1},setRight:function(t){this.right=t},interfaces_:function(){return[]},getClass:function(){return Ys}}),e(Us.prototype,{insert:function(){if(1===arguments.length){var t=arguments[0];return this.insert(t,null)}if(2===arguments.length){var e=arguments[0],n=arguments[1];if(null===this.root)return this.root=new Ys(e,n),this.root;if(this.tolerance>0){var i=this.findBestMatchNode(e);if(null!==i)return i.increment(),i}return this.insertExact(e,n)}},query:function(){var t=arguments,e=this;if(1===arguments.length){var n=arguments[0],i=new I;return this.query(n,i),i}if(2===arguments.length)if(arguments[0]instanceof C&&R(arguments[1],y))!function(){var n=t[0],i=t[1];e.queryNode(e.root,n,!0,{interfaces_:function(){return[ks]},visit:function(t){i.add(t)}})}();else if(arguments[0]instanceof C&&R(arguments[1],ks)){var r=arguments[0],s=arguments[1];this.queryNode(this.root,r,!0,s)}},queryNode:function(t,e,n,i){if(null===t)return null;var r=null,s=null,o=null;n?(r=e.getMinX(),s=e.getMaxX(),o=t.getX()):(r=e.getMinY(),s=e.getMaxY(),o=t.getY());var a=o>r,u=s>=o;a&&this.queryNode(t.getLeft(),e,!n,i),e.contains(t.getCoordinate())&&i.visit(t),u&&this.queryNode(t.getRight(),e,!n,i)},findBestMatchNode:function(t){var e=new Xs(t,this.tolerance);return this.query(e.queryEnvelope(),e),e.getNode()},isEmpty:function(){return null===this.root},insertExact:function(t,e){for(var n=this.root,i=this.root,r=!0,s=!0;null!==n;){if(null!==n){var o=t.distance(n.getCoordinate())<=this.tolerance;if(o)return n.increment(),n}s=r?t.x<n.getX():t.y<n.getY(),i=n,n=s?n.getLeft():n.getRight(),r=!r}this.numberOfNodes=this.numberOfNodes+1;var a=new Ys(t,e);return s?i.setLeft(a):i.setRight(a),a},interfaces_:function(){return[]},getClass:function(){return Us}}),Us.toCoordinates=function(){if(1===arguments.length){var t=arguments[0];return Us.toCoordinates(t,!1)}if(2===arguments.length){for(var e=arguments[0],n=arguments[1],i=new N,r=e.iterator();r.hasNext();)for(var s=r.next(),o=n?s.getCount():1,a=0;o>a;a++)i.add(s.getCoordinate(),!0);return i.toCoordinateArray()}},e(Xs.prototype,{visit:function(t){var e=this.p.distance(t.getCoordinate()),n=e<=this.tolerance;if(!n)return null;var i=!1;(null===this.matchNode||e<this.matchDist||null!==this.matchNode&&e===this.matchDist&&t.getCoordinate().compareTo(this.matchNode.getCoordinate())<1)&&(i=!0),i&&(this.matchNode=t,this.matchDist=e)},queryEnvelope:function(){var t=new C(this.p);return t.expandBy(this.tolerance),t},getNode:function(){return this.matchNode},interfaces_:function(){return[ks]},getClass:function(){return Xs}}),Us.BestMatchVisitor=Xs,e(Hs.prototype,{getInitialVertices:function(){return this.initialVertices},getKDT:function(){return this.kdt},enforceConstraints:function(){this.addConstraintVertices();var t=0,e=0;do e=this.enforceGabriel(this.segments),t++;while(e>0&&t<Hs.MAX_SPLIT_ITER)},insertSites:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.insertSite(n)}},getVertexFactory:function(){return this.vertexFactory},getPointArray:function(){for(var t=new Array(this.initialVertices.size()+this.segVertices.size()).fill(null),e=0,n=this.initialVertices.iterator();n.hasNext();){var i=n.next();t[e++]=i.getCoordinate()}for(var r=this.segVertices.iterator();r.hasNext();){var i=r.next();t[e++]=i.getCoordinate()}return t},setConstraints:function(t,e){this.segments=t,this.segVertices=e},computeConvexHull:function(){var t=new ie,e=this.getPointArray(),n=new me(e,t);this.convexHull=n.getConvexHull()},addConstraintVertices:function(){this.computeConvexHull(),this.insertSites(this.segVertices)},findNonGabrielPoint:function(t){var e=t.getStart(),n=t.getEnd(),i=new g((e.x+n.x)/2,(e.y+n.y)/2),s=e.distance(i),o=new C(i);o.expandBy(s);for(var a=this.kdt.query(o),u=null,l=r.MAX_VALUE,h=a.iterator();h.hasNext();){var c=h.next(),f=c.getCoordinate();if(!f.equals2D(e)&&!f.equals2D(n)){var d=i.distance(f);if(s>d){var p=d;(null===u||l>p)&&(u=f,l=p)}}}return u},getConstraintSegments:function(){return this.segments},setSplitPointFinder:function(t){this.splitFinder=t},getConvexHull:function(){return this.convexHull},getTolerance:function(){return this.tolerance},enforceGabriel:function(t){for(var e=new I,n=0,i=new I,r=t.iterator();r.hasNext();){var s=r.next(),o=this.findNonGabrielPoint(s);if(null!==o){this.splitPt=this.splitFinder.findSplitPoint(s,o);var a=this.createVertex(this.splitPt,s),u=(this.insertSite(a),new Vs(s.getStartX(),s.getStartY(),s.getStartZ(),a.getX(),a.getY(),a.getZ(),s.getData())),l=new Vs(a.getX(),a.getY(),a.getZ(),s.getEndX(),s.getEndY(),s.getEndZ(),s.getData());e.add(u),e.add(l),i.add(s),n+=1}}return t.removeAll(i),t.addAll(e),n},createVertex:function(){if(1===arguments.length){var t=arguments[0],e=null;return e=null!==this.vertexFactory?this.vertexFactory.createVertex(t,null):new Ps(t)}if(2===arguments.length){var n=arguments[0],i=arguments[1],e=null;return e=null!==this.vertexFactory?this.vertexFactory.createVertex(n,i):new Ps(n),e.setOnConstraint(!0),e}},getSubdivision:function(){return this.subdiv},computeBoundingBox:function(){var t=Hs.computeVertexEnvelope(this.initialVertices),e=Hs.computeVertexEnvelope(this.segVertices),n=new C(t);n.expandToInclude(e);var i=.2*n.getWidth(),r=.2*n.getHeight(),s=Math.max(i,r);this.computeAreaEnv=new C(n),this.computeAreaEnv.expandBy(s)},setVertexFactory:function(t){this.vertexFactory=t},formInitialDelaunay:function(){this.computeBoundingBox(),this.subdiv=new Fs(this.computeAreaEnv,this.tolerance),this.subdiv.setLocator(new Ms(this.subdiv)),this.incDel=new Os(this.subdiv),this.insertSites(this.initialVertices)},insertSite:function(){if(arguments[0]instanceof Ps){var t=arguments[0],e=this.kdt.insert(t.getCoordinate(),t);if(e.isRepeated()){var n=e.getData();return n.merge(t),n}return this.incDel.insertSite(t),t}if(arguments[0]instanceof g){var i=arguments[0];this.insertSite(this.createVertex(i))}},interfaces_:function(){return[]},getClass:function(){return Hs}}),Hs.computeVertexEnvelope=function(t){for(var e=new C,n=t.iterator();n.hasNext();){var i=n.next();e.expandToInclude(i.getCoordinate())}return e},Hs.MAX_SPLIT_ITER=99,e(Ws.prototype,{create:function(){if(null!==this.subdiv)return null;var t=Ws.envelope(this.siteCoords),e=Ws.toVertices(this.siteCoords);this.subdiv=new Fs(t,this.tolerance);var n=new Os(this.subdiv);n.insertSites(e)},setTolerance:function(t){this.tolerance=t},setSites:function(){if(arguments[0]instanceof B){var t=arguments[0];this.siteCoords=Ws.extractUniqueCoordinates(t)}else if(R(arguments[0],v)){var e=arguments[0];this.siteCoords=Ws.unique(H.toCoordinateArray(e))}},getEdges:function(t){return this.create(),this.subdiv.getEdges(t)},getSubdivision:function(){return this.create(),this.subdiv},getTriangles:function(t){return this.create(),this.subdiv.getTriangles(t)},interfaces_:function(){return[]},getClass:function(){return Ws}}),Ws.extractUniqueCoordinates=function(t){if(null===t)return new N;var e=t.getCoordinates();return Ws.unique(e)},Ws.envelope=function(t){for(var e=new C,n=t.iterator();n.hasNext();){var i=n.next();e.expandToInclude(i)}return e},Ws.unique=function(t){var e=H.copyDeep(t);ut.sort(e);var n=new N(e,!1);return n},Ws.toVertices=function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next();e.add(new Ts(i))}return e},e(js.prototype,{createSiteVertices:function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next();this.constraintVertexMap.containsKey(i)||e.add(new Ps(i))}return e},create:function(){if(null!==this.subdiv)return null;var t=Ws.envelope(this.siteCoords),e=new I;null!==this.constraintLines&&(t.expandToInclude(this.constraintLines.getEnvelopeInternal()),this.createVertices(this.constraintLines),e=js.createConstraintSegments(this.constraintLines));var n=this.createSiteVertices(this.siteCoords),i=new Hs(n,this.tolerance);i.setConstraints(e,new I(this.constraintVertexMap.values())),i.formInitialDelaunay(),i.enforceConstraints(),this.subdiv=i.getSubdivision()},setTolerance:function(t){this.tolerance=t},setConstraints:function(t){this.constraintLines=t},setSites:function(t){this.siteCoords=Ws.extractUniqueCoordinates(t)},getEdges:function(t){return this.create(),this.subdiv.getEdges(t)},getSubdivision:function(){return this.create(),this.subdiv},getTriangles:function(t){return this.create(),this.subdiv.getTriangles(t)},createVertices:function(t){for(var e=t.getCoordinates(),n=0;n<e.length;n++){var i=new Ps(e[n]);this.constraintVertexMap.put(e[n],i)}},interfaces_:function(){return[]},getClass:function(){return js}}),js.createConstraintSegments=function(){if(1===arguments.length){for(var t=arguments[0],e=kn.getLines(t),n=new I,i=e.iterator();i.hasNext();){var r=i.next();js.createConstraintSegments(r,n)}return n}if(2===arguments.length)for(var s=arguments[0],o=arguments[1],a=s.getCoordinates(),i=1;i<a.length;i++)o.add(new Vs(a[i-1],a[i]))},e(Ks.prototype,{create:function(){if(null!==this.subdiv)return null;var t=Ws.envelope(this.siteCoords);this.diagramEnv=t;var e=Math.max(this.diagramEnv.getWidth(),this.diagramEnv.getHeight());this.diagramEnv.expandBy(e),null!==this.clipEnv&&this.diagramEnv.expandToInclude(this.clipEnv);var n=Ws.toVertices(this.siteCoords);this.subdiv=new Fs(t,this.tolerance);var i=new Os(this.subdiv);i.insertSites(n)},getDiagram:function(t){this.create();var e=this.subdiv.getVoronoiDiagram(t);return Ks.clipGeometryCollection(e,this.diagramEnv)},setTolerance:function(t){this.tolerance=t},setSites:function(){if(arguments[0]instanceof B){var t=arguments[0];this.siteCoords=Ws.extractUniqueCoordinates(t)}else if(R(arguments[0],v)){var e=arguments[0];this.siteCoords=Ws.unique(H.toCoordinateArray(e))}},setClipEnvelope:function(t){this.clipEnv=t},getSubdivision:function(){return this.create(),this.subdiv},interfaces_:function(){return[]},getClass:function(){return Ks}}),Ks.clipGeometryCollection=function(t,e){for(var n=t.getFactory().toGeometry(e),i=new I,r=0;r<t.getNumGeometries();r++){var s=t.getGeometryN(r),o=null;e.contains(s.getEnvelopeInternal())?o=s:e.intersects(s.getEnvelopeInternal())&&(o=n.intersection(s),o.setUserData(s.getUserData())),null===o||o.isEmpty()||i.add(o)}return t.getFactory().createGeometryCollection(ie.toGeometryArray(i))};var Do=Object.freeze({ConformingDelaunayTriangulationBuilder:js,DelaunayTriangulationBuilder:Ws,VoronoiDiagramBuilder:Ks});e(Zs.prototype,{interfaces_:function(){return[]},getClass:function(){return Zs}}),Zs.union=function(t,e){if(t.isEmpty()||e.isEmpty()){if(t.isEmpty()&&e.isEmpty())return ii.createEmptyResult(ii.UNION,t,e,t.getFactory());if(t.isEmpty())return e.copy();if(e.isEmpty())return t.copy()}return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),si.overlayOp(t,e,ii.UNION)},e(B.prototype,{equalsTopo:function(t){return this.getEnvelopeInternal().equals(t.getEnvelopeInternal())?Yr.relate(this,t).isEquals(this.getDimension(),t.getDimension()):!1},union:function(){if(0===arguments.length)return jr.union(this);if(1===arguments.length){var t=arguments[0];return Zs.union(this,t)}},isValid:function(){return ls.isValid(this)},intersection:function(t){if(this.isEmpty()||t.isEmpty())return ii.createEmptyResult(ii.INTERSECTION,this,t,this.factory);if(this.isGeometryCollection()){var e=t;return hn.map(this,{interfaces_:function(){return[MapOp]},map:function(t){return t.intersection(e)}})}return this.checkNotGeometryCollection(this),this.checkNotGeometryCollection(t),si.overlayOp(this,t,ii.INTERSECTION)},covers:function(t){return Yr.covers(this,t)},coveredBy:function(t){return Yr.coveredBy(this,t)},touches:function(t){return Yr.touches(this,t)},intersects:function(t){return Yr.intersects(this,t)},within:function(t){return Yr.within(this,t)},overlaps:function(t){return Yr.overlaps(this,t)},disjoint:function(t){return Yr.disjoint(this,t)},crosses:function(t){return Yr.crosses(this,t)},buffer:function(){if(1===arguments.length){var t=arguments[0];return sr.bufferOp(this,t)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return sr.bufferOp(this,e,n)}if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];return sr.bufferOp(this,i,r,s)}},convexHull:function(){return new me(this).getConvexHull()},relate:function(){for(var t=arguments.length,e=Array(t),n=0;t>n;n++)e[n]=arguments[n];return Yr.relate.apply(Yr,[this].concat(e))},getCentroid:function(){if(this.isEmpty())return this.factory.createPoint();var t=ge.getCentroid(this);return this.createPointFromInternalCoord(t,this)},getInteriorPoint:function(){if(this.isEmpty())return this.factory.createPoint();var t=null,e=this.getDimension();if(0===e){var n=new li(this);t=n.getInteriorPoint()}else if(1===e){var n=new ui(this);t=n.getInteriorPoint()}else{var n=new oi(this);t=n.getInteriorPoint()}return this.createPointFromInternalCoord(t,this)},symDifference:function(t){if(this.isEmpty()||t.isEmpty()){if(this.isEmpty()&&t.isEmpty())return ii.createEmptyResult(ii.SYMDIFFERENCE,this,t,this.factory);if(this.isEmpty())return t.copy();if(t.isEmpty())return this.copy()}return this.checkNotGeometryCollection(this),this.checkNotGeometryCollection(t),si.overlayOp(this,t,ii.SYMDIFFERENCE)},createPointFromInternalCoord:function(t,e){return e.getPrecisionModel().makePrecise(t),e.getFactory().createPoint(t)},toText:function(){var t=new se;return t.write(this)},toString:function(){this.toText()},contains:function(t){return Yr.contains(this,t)},difference:function(t){return this.isEmpty()?ii.createEmptyResult(ii.DIFFERENCE,this,t,this.factory):t.isEmpty()?this.copy():(this.checkNotGeometryCollection(this),this.checkNotGeometryCollection(t),si.overlayOp(this,t,ii.DIFFERENCE))},isSimple:function(){var t=new Gi(this);return t.isSimple()},isWithinDistance:function(t,e){var n=this.getEnvelopeInternal().distance(t.getEnvelopeInternal());return n>e?!1:hr.isWithinDistance(this,t,e)},distance:function(t){return hr.distance(this,t)},isEquivalentClass:function(t){return this.getClass()===t.getClass()}});var Ao="1.1.2 (248dab8)";t.version=Ao,t.algorithm=co,t.densify=fo,t.dissolve=go,t.geom=lo,t.index=mo,t.io=Io,t.noding=No,t.operation=Oo,t.precision=_o,t.simplify=Mo,t.triangulate=Do});

},{}],21:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],22:[function(require,module,exports){
var bbox = require('turf-bbox'),
    point = require('turf-helpers').point;

/**
 * Takes a {@link FeatureCollection} and returns the absolute center point of all features.
 *
 * @name center
 * @param {FeatureCollection} features input features
 * @return {Feature<Point>} a Point feature at the
 * absolute center point of all input features
 * @example
 * var features = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.522259, 35.4691]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.502754, 35.463455]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.508269, 35.463245]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.516809, 35.465779]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.515372, 35.467072]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.509363, 35.463053]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.511123, 35.466601]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.518547, 35.469327]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.519706, 35.469659]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.517839, 35.466998]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.508678, 35.464942]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-97.514914, 35.463453]
 *       }
 *     }
 *   ]
 * };
 *
 * var centerPt = turf.center(features);
 * centerPt.properties['marker-size'] = 'large';
 * centerPt.properties['marker-color'] = '#000';
 *
 * var resultFeatures = features.features.concat(centerPt);
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": resultFeatures
 * };
 *
 * //=result
 */

module.exports = function (layer) {
    var ext = bbox(layer);
    var x = (ext[0] + ext[2]) / 2;
    var y = (ext[1] + ext[3]) / 2;
    return point([x, y]);
};

},{"turf-bbox":23,"turf-helpers":24}],23:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-bbox");

},{"/Users/tmcw/src/turf/packages/turf-bbox":11}],24:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],25:[function(require,module,exports){
var each = require('turf-meta').coordEach;
var point = require('turf-helpers').point;

/**
 * Takes one or more features and calculates the centroid using
 * the mean of all vertices.
 * This lessens the effect of small islands and artifacts when calculating
 * the centroid of a set of polygons.
 *
 * @name centroid
 * @param {(Feature|FeatureCollection)} features input features
 * @return {Feature<Point>} the centroid of the input features
 * @example
 * var poly = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [105.818939,21.004714],
 *       [105.818939,21.061754],
 *       [105.890007,21.061754],
 *       [105.890007,21.004714],
 *       [105.818939,21.004714]
 *     ]]
 *   }
 * };
 *
 * var centroidPt = turf.centroid(poly);
 *
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": [poly, centroidPt]
 * };
 *
 * //=result
 */
module.exports = function (features) {
    var xSum = 0, ySum = 0, len = 0;
    each(features, function (coord) {
        xSum += coord[0];
        ySum += coord[1];
        len++;
    }, true);
    return point([xSum / len, ySum / len]);
};

},{"turf-helpers":26,"turf-meta":27}],26:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],27:[function(require,module,exports){
arguments[4][12][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-meta":98,"dup":12}],28:[function(require,module,exports){
var inside = require('turf-inside');

/**
 * Joins attributes FeatureCollection of polygons with a FeatureCollection of
 * points. Given an `inProperty` on points and an `outProperty` for polygons,
 * this finds every point that lies within each polygon, collects the `inProperty`
 * values from those points, and adds them as an array to `outProperty` on the
 * polygon.
 *
 * @name collect
 * @param {FeatureCollection<Polygon>} polygons polygons with values on which to aggregate
 * @param {FeatureCollection<Point>} points points to be aggregated
 * @param {string} inProperty property to be nested from
 * @param {string} outProperty property to be nested into
 * @return {FeatureCollection<Polygon>} polygons with properties listed based on `outField`
 * @example
 * var poly1 = polygon([[[0,0],[10,0],[10,10],[0,10],[0,0]]]);
 * var poly2 = polygon([[[10,0],[20,10],[20,20],[20,0],[10,0]]]);
 * var polyFC = featurecollection([poly1, poly2]);
 * var pt1 = point([5,5], {population: 200});
 * var pt2 = point([1,3], {population: 600});
 * var pt3 = point([14,2], {population: 100});
 * var pt4 = point([13,1], {population: 200});
 * var pt5 = point([19,7], {population: 300});
 * var ptFC = featurecollection([pt1, pt2, pt3, pt4, pt5]);
 * var aggregated = aggregate(polyFC, ptFC, 'population', 'values');
 *
 * aggregated.features[0].properties.values // => [200, 600]);
 */
module.exports = function collect(polygons, points, inProperty, outProperty) {
    polygons.features.forEach(function (poly) {
        var values = points.features.filter(function (pt) {
            return inside(pt, poly);
        }).map(function (pt) {
            return pt.properties[inProperty];
        });

        if (!poly.properties) {
            poly.properties = {};
        }

        poly.properties[outProperty] = values;
    });

    return polygons;
};

},{"turf-inside":29}],29:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-inside");

},{"/Users/tmcw/src/turf/packages/turf-inside":75}],30:[function(require,module,exports){
var meta = require('turf-meta');

/**
 * Combines a {@link FeatureCollection} of {@link Point},
 * {@link LineString}, or {@link Polygon} features
 * into {@link MultiPoint}, {@link MultiLineString}, or
 * {@link MultiPolygon} features.
 *
 * @name combine
 * @param {FeatureCollection<(Point|LineString|Polygon)>} fc a FeatureCollection of any type
 * @return {FeatureCollection<(MultiPoint|MultiLineString|MultiPolygon)>} a FeatureCollection of corresponding type to input
 * @example
 * var fc = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [19.026432, 47.49134]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [19.074497, 47.509548]
 *       }
 *     }
 *   ]
 * };
 *
 * var combined = turf.combine(fc);
 *
 * //=combined
 */

module.exports = function (fc) {
    var groups = {
        MultiPoint: {coordinates: [], properties: []},
        MultiLineString: {coordinates: [], properties: []},
        MultiPolygon: {coordinates: [], properties: []}
    };

    var multiMapping = Object.keys(groups).reduce(function (memo, item) {
        memo[item.replace('Multi', '')] = item;
        return memo;
    }, {});

    function addToGroup(feature, key, multi) {
        if (!multi) {
            groups[key].coordinates.push(feature.geometry.coordinates);
        } else {
            groups[key].coordinates = groups[key].coordinates.concat(feature.geometry.coordinates);
        }
        groups[key].properties.push(feature.properties);
    }

    meta.featureEach(fc, function (feature) {
        if (!feature.geometry) return;
        if (groups[feature.geometry.type]) {
            addToGroup(feature, feature.geometry.type, true);
        } else if (multiMapping[feature.geometry.type]) {
            addToGroup(feature, multiMapping[feature.geometry.type], false);
        }
    });

    return {
        type: 'FeatureCollection',
        features: Object.keys(groups)
            .filter(function (key) {
                return groups[key].coordinates.length;
            })
            .sort()
            .map(function (key) {
                return {
                    type: 'Feature',
                    properties: {
                        collectedProperties: groups[key].properties
                    },
                    geometry: {
                        type: key,
                        coordinates: groups[key].coordinates
                    }
                };
            })
    };
};

},{"turf-meta":31}],31:[function(require,module,exports){
arguments[4][12][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-meta":98,"dup":12}],32:[function(require,module,exports){
// 1. run tin on points
// 2. calculate lenth of all edges and area of all triangles
// 3. remove triangles that fail the max length test
// 4. buffer the results slightly
// 5. merge the results
var tin = require('turf-tin');
var union = require('turf-union');
var distance = require('turf-distance');

/**
 * Takes a set of {@link Point|points} and returns a concave hull polygon.
 *
 * Internally, this uses [turf-tin](https://github.com/Turfjs/turf-tin) to generate geometries.
 *
 * @param {FeatureCollection<Point>} points input points
 * @param {number} maxEdge the size of an edge necessary for part of the
 * hull to become concave (in miles)
 * @param {string} units used for maxEdge distance (miles or kilometers)
 * @returns {Feature<Polygon>} a concave hull
 * @throws {Error} if maxEdge parameter is missing
 * @throws {Error} if units parameter is missing
 * @example
 * var points = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-63.601226, 44.642643]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-63.591442, 44.651436]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-63.580799, 44.648749]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-63.573589, 44.641788]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-63.587665, 44.64533]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-63.595218, 44.64765]
 *       }
 *     }
 *   ]
 * };
 *
 * var hull = turf.concave(points, 1, 'miles');
 *
 * var resultFeatures = points.features.concat(hull);
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": resultFeatures
 * };
 *
 * //=result
 */
function concave(points, maxEdge, units) {
    if (typeof maxEdge !== 'number') throw new Error('maxEdge parameter is required');
    if (typeof units !== 'string') throw new Error('units parameter is required');

    var tinPolys = tin(points);
    var filteredPolys = tinPolys.features.filter(filterTriangles);
    tinPolys.features = filteredPolys;

    function filterTriangles(triangle) {
        var pt1 = triangle.geometry.coordinates[0][0];
        var pt2 = triangle.geometry.coordinates[0][1];
        var pt3 = triangle.geometry.coordinates[0][2];
        var dist1 = distance(pt1, pt2, units);
        var dist2 = distance(pt2, pt3, units);
        var dist3 = distance(pt1, pt3, units);
        return (dist1 <= maxEdge && dist2 <= maxEdge && dist3 <= maxEdge);
    }

    return merge(tinPolys);
}

function merge(polygons) {
    var merged = JSON.parse(JSON.stringify(polygons.features[0])),
        features = polygons.features;

    for (var i = 0, len = features.length; i < len; i++) {
        var poly = features[i];
        if (poly.geometry) {
            merged = union(merged, poly);
        }
    }
    return merged;
}

module.exports = concave;

},{"turf-distance":33,"turf-tin":34,"turf-union":35}],33:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],34:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-tin");

},{"/Users/tmcw/src/turf/packages/turf-tin":136}],35:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-union");

},{"/Users/tmcw/src/turf/packages/turf-union":141}],36:[function(require,module,exports){
var each = require('turf-meta').coordEach,
    convexHull = require('convex-hull'),
    polygon = require('turf-helpers').polygon;

/**
 * Takes a set of {@link Point|points} and returns a
 * [convex hull](http://en.wikipedia.org/wiki/Convex_hull) polygon.
 *
 * Internally this uses
 * the [convex-hull](https://github.com/mikolalysenko/convex-hull) module that
 * implements a [monotone chain hull](http://en.wikibooks.org/wiki/Algorithm_Implementation/Geometry/Convex_hull/Monotone_chain).
 *
 * @name convex
 * @param {FeatureCollection<Point>} featurecollection input points
 * @returns {Feature<Polygon>} a convex hull
 * @example
 * var points = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [10.195312, 43.755225]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [10.404052, 43.8424511]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [10.579833, 43.659924]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [10.360107, 43.516688]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [10.14038, 43.588348]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [10.195312, 43.755225]
 *       }
 *     }
 *   ]
 * };
 *
 * var hull = turf.convex(points);
 *
 * var resultFeatures = points.features.concat(hull);
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": resultFeatures
 * };
 *
 * //=result
 */
module.exports = function (featurecollection) {
    var points = [];
    each(featurecollection, function (coord) { points.push(coord); });
    var hull = convexHull(points);
    if (hull.length > 0) {
        var ring = [];
        for (var i = 0; i < hull.length; i++) {
            ring.push(points[hull[i][0]]);
        }
        ring.push(points[hull[hull.length - 1][1]]);
        return polygon([ring]);
    }
    return undefined;
};

},{"convex-hull":39,"turf-helpers":50,"turf-meta":51}],37:[function(require,module,exports){
'use strict'

module.exports = affineHull

var orient = require('robust-orientation')

function linearlyIndependent(points, d) {
  var nhull = new Array(d+1)
  for(var i=0; i<points.length; ++i) {
    nhull[i] = points[i]
  }
  for(var i=0; i<=points.length; ++i) {
    for(var j=points.length; j<=d; ++j) {
      var x = new Array(d)
      for(var k=0; k<d; ++k) {
        x[k] = Math.pow(j+1-i, k)
      }
      nhull[j] = x
    }
    var o = orient.apply(void 0, nhull)
    if(o) {
      return true
    }
  }
  return false
}

function affineHull(points) {
  var n = points.length
  if(n === 0) {
    return []
  }
  if(n === 1) {
    return [0]
  }
  var d = points[0].length
  var frame = [ points[0] ]
  var index = [ 0 ]
  for(var i=1; i<n; ++i) {
    frame.push(points[i])
    if(!linearlyIndependent(frame, d)) {
      frame.pop()
      continue
    }
    index.push(i)
    if(index.length === d+1) {
      return index
    }
  }
  return index
}
},{"robust-orientation":45}],38:[function(require,module,exports){
/**
 * Bit twiddling hacks for JavaScript.
 *
 * Author: Mikola Lysenko
 *
 * Ported from Stanford bit twiddling hack library:
 *    http://graphics.stanford.edu/~seander/bithacks.html
 */

"use strict"; "use restrict";

//Number of bits in an integer
var INT_BITS = 32;

//Constants
exports.INT_BITS  = INT_BITS;
exports.INT_MAX   =  0x7fffffff;
exports.INT_MIN   = -1<<(INT_BITS-1);

//Returns -1, 0, +1 depending on sign of x
exports.sign = function(v) {
  return (v > 0) - (v < 0);
}

//Computes absolute value of integer
exports.abs = function(v) {
  var mask = v >> (INT_BITS-1);
  return (v ^ mask) - mask;
}

//Computes minimum of integers x and y
exports.min = function(x, y) {
  return y ^ ((x ^ y) & -(x < y));
}

//Computes maximum of integers x and y
exports.max = function(x, y) {
  return x ^ ((x ^ y) & -(x < y));
}

//Checks if a number is a power of two
exports.isPow2 = function(v) {
  return !(v & (v-1)) && (!!v);
}

//Computes log base 2 of v
exports.log2 = function(v) {
  var r, shift;
  r =     (v > 0xFFFF) << 4; v >>>= r;
  shift = (v > 0xFF  ) << 3; v >>>= shift; r |= shift;
  shift = (v > 0xF   ) << 2; v >>>= shift; r |= shift;
  shift = (v > 0x3   ) << 1; v >>>= shift; r |= shift;
  return r | (v >> 1);
}

//Computes log base 10 of v
exports.log10 = function(v) {
  return  (v >= 1000000000) ? 9 : (v >= 100000000) ? 8 : (v >= 10000000) ? 7 :
          (v >= 1000000) ? 6 : (v >= 100000) ? 5 : (v >= 10000) ? 4 :
          (v >= 1000) ? 3 : (v >= 100) ? 2 : (v >= 10) ? 1 : 0;
}

//Counts number of bits
exports.popCount = function(v) {
  v = v - ((v >>> 1) & 0x55555555);
  v = (v & 0x33333333) + ((v >>> 2) & 0x33333333);
  return ((v + (v >>> 4) & 0xF0F0F0F) * 0x1010101) >>> 24;
}

//Counts number of trailing zeros
function countTrailingZeros(v) {
  var c = 32;
  v &= -v;
  if (v) c--;
  if (v & 0x0000FFFF) c -= 16;
  if (v & 0x00FF00FF) c -= 8;
  if (v & 0x0F0F0F0F) c -= 4;
  if (v & 0x33333333) c -= 2;
  if (v & 0x55555555) c -= 1;
  return c;
}
exports.countTrailingZeros = countTrailingZeros;

//Rounds to next power of 2
exports.nextPow2 = function(v) {
  v += v === 0;
  --v;
  v |= v >>> 1;
  v |= v >>> 2;
  v |= v >>> 4;
  v |= v >>> 8;
  v |= v >>> 16;
  return v + 1;
}

//Rounds down to previous power of 2
exports.prevPow2 = function(v) {
  v |= v >>> 1;
  v |= v >>> 2;
  v |= v >>> 4;
  v |= v >>> 8;
  v |= v >>> 16;
  return v - (v>>>1);
}

//Computes parity of word
exports.parity = function(v) {
  v ^= v >>> 16;
  v ^= v >>> 8;
  v ^= v >>> 4;
  v &= 0xf;
  return (0x6996 >>> v) & 1;
}

var REVERSE_TABLE = new Array(256);

(function(tab) {
  for(var i=0; i<256; ++i) {
    var v = i, r = i, s = 7;
    for (v >>>= 1; v; v >>>= 1) {
      r <<= 1;
      r |= v & 1;
      --s;
    }
    tab[i] = (r << s) & 0xff;
  }
})(REVERSE_TABLE);

//Reverse bits in a 32 bit word
exports.reverse = function(v) {
  return  (REVERSE_TABLE[ v         & 0xff] << 24) |
          (REVERSE_TABLE[(v >>> 8)  & 0xff] << 16) |
          (REVERSE_TABLE[(v >>> 16) & 0xff] << 8)  |
           REVERSE_TABLE[(v >>> 24) & 0xff];
}

//Interleave bits of 2 coordinates with 16 bits.  Useful for fast quadtree codes
exports.interleave2 = function(x, y) {
  x &= 0xFFFF;
  x = (x | (x << 8)) & 0x00FF00FF;
  x = (x | (x << 4)) & 0x0F0F0F0F;
  x = (x | (x << 2)) & 0x33333333;
  x = (x | (x << 1)) & 0x55555555;

  y &= 0xFFFF;
  y = (y | (y << 8)) & 0x00FF00FF;
  y = (y | (y << 4)) & 0x0F0F0F0F;
  y = (y | (y << 2)) & 0x33333333;
  y = (y | (y << 1)) & 0x55555555;

  return x | (y << 1);
}

//Extracts the nth interleaved component
exports.deinterleave2 = function(v, n) {
  v = (v >>> n) & 0x55555555;
  v = (v | (v >>> 1))  & 0x33333333;
  v = (v | (v >>> 2))  & 0x0F0F0F0F;
  v = (v | (v >>> 4))  & 0x00FF00FF;
  v = (v | (v >>> 16)) & 0x000FFFF;
  return (v << 16) >> 16;
}


//Interleave bits of 3 coordinates, each with 10 bits.  Useful for fast octree codes
exports.interleave3 = function(x, y, z) {
  x &= 0x3FF;
  x  = (x | (x<<16)) & 4278190335;
  x  = (x | (x<<8))  & 251719695;
  x  = (x | (x<<4))  & 3272356035;
  x  = (x | (x<<2))  & 1227133513;

  y &= 0x3FF;
  y  = (y | (y<<16)) & 4278190335;
  y  = (y | (y<<8))  & 251719695;
  y  = (y | (y<<4))  & 3272356035;
  y  = (y | (y<<2))  & 1227133513;
  x |= (y << 1);

  z &= 0x3FF;
  z  = (z | (z<<16)) & 4278190335;
  z  = (z | (z<<8))  & 251719695;
  z  = (z | (z<<4))  & 3272356035;
  z  = (z | (z<<2))  & 1227133513;

  return x | (z << 2);
}

//Extracts nth interleaved component of a 3-tuple
exports.deinterleave3 = function(v, n) {
  v = (v >>> n)       & 1227133513;
  v = (v | (v>>>2))   & 3272356035;
  v = (v | (v>>>4))   & 251719695;
  v = (v | (v>>>8))   & 4278190335;
  v = (v | (v>>>16))  & 0x3FF;
  return (v<<22)>>22;
}

//Computes next combination in colexicographic order (this is mistakenly called nextPermutation on the bit twiddling hacks page)
exports.nextCombination = function(v) {
  var t = v | (v - 1);
  return (t + 1) | (((~t & -~t) - 1) >>> (countTrailingZeros(v) + 1));
}


},{}],39:[function(require,module,exports){
"use strict"

var convexHull1d = require('./lib/ch1d')
var convexHull2d = require('./lib/ch2d')
var convexHullnd = require('./lib/chnd')

module.exports = convexHull

function convexHull(points) {
  var n = points.length
  if(n === 0) {
    return []
  } else if(n === 1) {
    return [[0]]
  }
  var d = points[0].length
  if(d === 0) {
    return []
  } else if(d === 1) {
    return convexHull1d(points)
  } else if(d === 2) {
    return convexHull2d(points)
  }
  return convexHullnd(points, d)
}
},{"./lib/ch1d":40,"./lib/ch2d":41,"./lib/chnd":42}],40:[function(require,module,exports){
"use strict"

module.exports = convexHull1d

function convexHull1d(points) {
  var lo = 0
  var hi = 0
  for(var i=1; i<points.length; ++i) {
    if(points[i][0] < points[lo][0]) {
      lo = i
    }
    if(points[i][0] > points[hi][0]) {
      hi = i
    }
  }
  if(lo < hi) {
    return [[lo], [hi]]
  } else if(lo > hi) {
    return [[hi], [lo]]
  } else {
    return [[lo]]
  }
}
},{}],41:[function(require,module,exports){
'use strict'

module.exports = convexHull2D

var monotoneHull = require('monotone-convex-hull-2d')

function convexHull2D(points) {
  var hull = monotoneHull(points)
  var h = hull.length
  if(h <= 2) {
    return []
  }
  var edges = new Array(h)
  var a = hull[h-1]
  for(var i=0; i<h; ++i) {
    var b = hull[i]
    edges[i] = [a,b]
    a = b
  }
  return edges
}

},{"monotone-convex-hull-2d":44}],42:[function(require,module,exports){
'use strict'

module.exports = convexHullnD

var ich = require('incremental-convex-hull')
var aff = require('affine-hull')

function permute(points, front) {
  var n = points.length
  var npoints = new Array(n)
  for(var i=0; i<front.length; ++i) {
    npoints[i] = points[front[i]]
  }
  var ptr = front.length
  for(var i=0; i<n; ++i) {
    if(front.indexOf(i) < 0) {
      npoints[ptr++] = points[i]
    }
  }
  return npoints
}

function invPermute(cells, front) {
  var nc = cells.length
  var nf = front.length
  for(var i=0; i<nc; ++i) {
    var c = cells[i]
    for(var j=0; j<c.length; ++j) {
      var x = c[j]
      if(x < nf) {
        c[j] = front[x]
      } else {
        x = x - nf
        for(var k=0; k<nf; ++k) {
          if(x >= front[k]) {
            x += 1
          }
        }
        c[j] = x
      }
    }
  }
  return cells
}

function convexHullnD(points, d) {
  try {
    return ich(points, true)
  } catch(e) {
    //If point set is degenerate, try to find a basis and rerun it
    var ah = aff(points)
    if(ah.length <= d) {
      //No basis, no try
      return []
    }
    var npoints = permute(points, ah)
    var nhull   = ich(npoints, true)
    return invPermute(nhull, ah)
  }
}
},{"affine-hull":37,"incremental-convex-hull":43}],43:[function(require,module,exports){
"use strict"

//High level idea:
// 1. Use Clarkson's incremental construction to find convex hull
// 2. Point location in triangulation by jump and walk

module.exports = incrementalConvexHull

var orient = require("robust-orientation")
var compareCell = require("simplicial-complex").compareCells

function compareInt(a, b) {
  return a - b
}

function Simplex(vertices, adjacent, boundary) {
  this.vertices = vertices
  this.adjacent = adjacent
  this.boundary = boundary
  this.lastVisited = -1
}

Simplex.prototype.flip = function() {
  var t = this.vertices[0]
  this.vertices[0] = this.vertices[1]
  this.vertices[1] = t
  var u = this.adjacent[0]
  this.adjacent[0] = this.adjacent[1]
  this.adjacent[1] = u
}

function GlueFacet(vertices, cell, index) {
  this.vertices = vertices
  this.cell = cell
  this.index = index
}

function compareGlue(a, b) {
  return compareCell(a.vertices, b.vertices)
}

function bakeOrient(d) {
  var code = ["function orient(){var tuple=this.tuple;return test("]
  for(var i=0; i<=d; ++i) {
    if(i > 0) {
      code.push(",")
    }
    code.push("tuple[", i, "]")
  }
  code.push(")}return orient")
  var proc = new Function("test", code.join(""))
  var test = orient[d+1]
  if(!test) {
    test = orient
  }
  return proc(test)
}

var BAKED = []

function Triangulation(dimension, vertices, simplices) {
  this.dimension = dimension
  this.vertices = vertices
  this.simplices = simplices
  this.interior = simplices.filter(function(c) {
    return !c.boundary
  })

  this.tuple = new Array(dimension+1)
  for(var i=0; i<=dimension; ++i) {
    this.tuple[i] = this.vertices[i]
  }

  var o = BAKED[dimension]
  if(!o) {
    o = BAKED[dimension] = bakeOrient(dimension)
  }
  this.orient = o
}

var proto = Triangulation.prototype

//Degenerate situation where we are on boundary, but coplanar to face
proto.handleBoundaryDegeneracy = function(cell, point) {
  var d = this.dimension
  var n = this.vertices.length - 1
  var tuple = this.tuple
  var verts = this.vertices

  //Dumb solution: Just do dfs from boundary cell until we find any peak, or terminate
  var toVisit = [ cell ]
  cell.lastVisited = -n
  while(toVisit.length > 0) {
    cell = toVisit.pop()
    var cellVerts = cell.vertices
    var cellAdj = cell.adjacent
    for(var i=0; i<=d; ++i) {
      var neighbor = cellAdj[i]
      if(!neighbor.boundary || neighbor.lastVisited <= -n) {
        continue
      }
      var nv = neighbor.vertices
      for(var j=0; j<=d; ++j) {
        var vv = nv[j]
        if(vv < 0) {
          tuple[j] = point
        } else {
          tuple[j] = verts[vv]
        }
      }
      var o = this.orient()
      if(o > 0) {
        return neighbor
      }
      neighbor.lastVisited = -n
      if(o === 0) {
        toVisit.push(neighbor)
      }
    }
  }
  return null
}

proto.walk = function(point, random) {
  //Alias local properties
  var n = this.vertices.length - 1
  var d = this.dimension
  var verts = this.vertices
  var tuple = this.tuple

  //Compute initial jump cell
  var initIndex = random ? (this.interior.length * Math.random())|0 : (this.interior.length-1)
  var cell = this.interior[ initIndex ]

  //Start walking
outerLoop:
  while(!cell.boundary) {
    var cellVerts = cell.vertices
    var cellAdj = cell.adjacent

    for(var i=0; i<=d; ++i) {
      tuple[i] = verts[cellVerts[i]]
    }
    cell.lastVisited = n

    //Find farthest adjacent cell
    for(var i=0; i<=d; ++i) {
      var neighbor = cellAdj[i]
      if(neighbor.lastVisited >= n) {
        continue
      }
      var prev = tuple[i]
      tuple[i] = point
      var o = this.orient()
      tuple[i] = prev
      if(o < 0) {
        cell = neighbor
        continue outerLoop
      } else {
        if(!neighbor.boundary) {
          neighbor.lastVisited = n
        } else {
          neighbor.lastVisited = -n
        }
      }
    }
    return
  }

  return cell
}

proto.addPeaks = function(point, cell) {
  var n = this.vertices.length - 1
  var d = this.dimension
  var verts = this.vertices
  var tuple = this.tuple
  var interior = this.interior
  var simplices = this.simplices

  //Walking finished at boundary, time to add peaks
  var tovisit = [ cell ]

  //Stretch initial boundary cell into a peak
  cell.lastVisited = n
  cell.vertices[cell.vertices.indexOf(-1)] = n
  cell.boundary = false
  interior.push(cell)

  //Record a list of all new boundaries created by added peaks so we can glue them together when we are all done
  var glueFacets = []

  //Do a traversal of the boundary walking outward from starting peak
  while(tovisit.length > 0) {
    //Pop off peak and walk over adjacent cells
    var cell = tovisit.pop()
    var cellVerts = cell.vertices
    var cellAdj = cell.adjacent
    var indexOfN = cellVerts.indexOf(n)
    if(indexOfN < 0) {
      continue
    }

    for(var i=0; i<=d; ++i) {
      if(i === indexOfN) {
        continue
      }

      //For each boundary neighbor of the cell
      var neighbor = cellAdj[i]
      if(!neighbor.boundary || neighbor.lastVisited >= n) {
        continue
      }

      var nv = neighbor.vertices

      //Test if neighbor is a peak
      if(neighbor.lastVisited !== -n) {
        //Compute orientation of p relative to each boundary peak
        var indexOfNeg1 = 0
        for(var j=0; j<=d; ++j) {
          if(nv[j] < 0) {
            indexOfNeg1 = j
            tuple[j] = point
          } else {
            tuple[j] = verts[nv[j]]
          }
        }
        var o = this.orient()

        //Test if neighbor cell is also a peak
        if(o > 0) {
          nv[indexOfNeg1] = n
          neighbor.boundary = false
          interior.push(neighbor)
          tovisit.push(neighbor)
          neighbor.lastVisited = n
          continue
        } else {
          neighbor.lastVisited = -n
        }
      }

      var na = neighbor.adjacent

      //Otherwise, replace neighbor with new face
      var vverts = cellVerts.slice()
      var vadj = cellAdj.slice()
      var ncell = new Simplex(vverts, vadj, true)
      simplices.push(ncell)

      //Connect to neighbor
      var opposite = na.indexOf(cell)
      if(opposite < 0) {
        continue
      }
      na[opposite] = ncell
      vadj[indexOfN] = neighbor

      //Connect to cell
      vverts[i] = -1
      vadj[i] = cell
      cellAdj[i] = ncell

      //Flip facet
      ncell.flip()

      //Add to glue list
      for(var j=0; j<=d; ++j) {
        var uu = vverts[j]
        if(uu < 0 || uu === n) {
          continue
        }
        var nface = new Array(d-1)
        var nptr = 0
        for(var k=0; k<=d; ++k) {
          var vv = vverts[k]
          if(vv < 0 || k === j) {
            continue
          }
          nface[nptr++] = vv
        }
        glueFacets.push(new GlueFacet(nface, ncell, j))
      }
    }
  }

  //Glue boundary facets together
  glueFacets.sort(compareGlue)

  for(var i=0; i+1<glueFacets.length; i+=2) {
    var a = glueFacets[i]
    var b = glueFacets[i+1]
    var ai = a.index
    var bi = b.index
    if(ai < 0 || bi < 0) {
      continue
    }
    a.cell.adjacent[a.index] = b.cell
    b.cell.adjacent[b.index] = a.cell
  }
}

proto.insert = function(point, random) {
  //Add point
  var verts = this.vertices
  verts.push(point)

  var cell = this.walk(point, random)
  if(!cell) {
    return
  }

  //Alias local properties
  var d = this.dimension
  var tuple = this.tuple

  //Degenerate case: If point is coplanar to cell, then walk until we find a non-degenerate boundary
  for(var i=0; i<=d; ++i) {
    var vv = cell.vertices[i]
    if(vv < 0) {
      tuple[i] = point
    } else {
      tuple[i] = verts[vv]
    }
  }
  var o = this.orient(tuple)
  if(o < 0) {
    return
  } else if(o === 0) {
    cell = this.handleBoundaryDegeneracy(cell, point)
    if(!cell) {
      return
    }
  }

  //Add peaks
  this.addPeaks(point, cell)
}

//Extract all boundary cells
proto.boundary = function() {
  var d = this.dimension
  var boundary = []
  var cells = this.simplices
  var nc = cells.length
  for(var i=0; i<nc; ++i) {
    var c = cells[i]
    if(c.boundary) {
      var bcell = new Array(d)
      var cv = c.vertices
      var ptr = 0
      var parity = 0
      for(var j=0; j<=d; ++j) {
        if(cv[j] >= 0) {
          bcell[ptr++] = cv[j]
        } else {
          parity = j&1
        }
      }
      if(parity === (d&1)) {
        var t = bcell[0]
        bcell[0] = bcell[1]
        bcell[1] = t
      }
      boundary.push(bcell)
    }
  }
  return boundary
}

function incrementalConvexHull(points, randomSearch) {
  var n = points.length
  if(n === 0) {
    throw new Error("Must have at least d+1 points")
  }
  var d = points[0].length
  if(n <= d) {
    throw new Error("Must input at least d+1 points")
  }

  //FIXME: This could be degenerate, but need to select d+1 non-coplanar points to bootstrap process
  var initialSimplex = points.slice(0, d+1)

  //Make sure initial simplex is positively oriented
  var o = orient.apply(void 0, initialSimplex)
  if(o === 0) {
    throw new Error("Input not in general position")
  }
  var initialCoords = new Array(d+1)
  for(var i=0; i<=d; ++i) {
    initialCoords[i] = i
  }
  if(o < 0) {
    initialCoords[0] = 1
    initialCoords[1] = 0
  }

  //Create initial topological index, glue pointers together (kind of messy)
  var initialCell = new Simplex(initialCoords, new Array(d+1), false)
  var boundary = initialCell.adjacent
  var list = new Array(d+2)
  for(var i=0; i<=d; ++i) {
    var verts = initialCoords.slice()
    for(var j=0; j<=d; ++j) {
      if(j === i) {
        verts[j] = -1
      }
    }
    var t = verts[0]
    verts[0] = verts[1]
    verts[1] = t
    var cell = new Simplex(verts, new Array(d+1), true)
    boundary[i] = cell
    list[i] = cell
  }
  list[d+1] = initialCell
  for(var i=0; i<=d; ++i) {
    var verts = boundary[i].vertices
    var adj = boundary[i].adjacent
    for(var j=0; j<=d; ++j) {
      var v = verts[j]
      if(v < 0) {
        adj[j] = initialCell
        continue
      }
      for(var k=0; k<=d; ++k) {
        if(boundary[k].vertices.indexOf(v) < 0) {
          adj[j] = boundary[k]
        }
      }
    }
  }

  //Initialize triangles
  var triangles = new Triangulation(d, initialSimplex, list)

  //Insert remaining points
  var useRandom = !!randomSearch
  for(var i=d+1; i<n; ++i) {
    triangles.insert(points[i], useRandom)
  }

  //Extract boundary cells
  return triangles.boundary()
}
},{"robust-orientation":45,"simplicial-complex":49}],44:[function(require,module,exports){
'use strict'

module.exports = monotoneConvexHull2D

var orient = require('robust-orientation')[3]

function monotoneConvexHull2D(points) {
  var n = points.length

  if(n < 3) {
    var result = new Array(n)
    for(var i=0; i<n; ++i) {
      result[i] = i
    }

    if(n === 2 &&
       points[0][0] === points[1][0] &&
       points[0][1] === points[1][1]) {
      return [0]
    }

    return result
  }

  //Sort point indices along x-axis
  var sorted = new Array(n)
  for(var i=0; i<n; ++i) {
    sorted[i] = i
  }
  sorted.sort(function(a,b) {
    var d = points[a][0]-points[b][0]
    if(d) {
      return d
    }
    return points[a][1] - points[b][1]
  })

  //Construct upper and lower hulls
  var lower = [sorted[0], sorted[1]]
  var upper = [sorted[0], sorted[1]]

  for(var i=2; i<n; ++i) {
    var idx = sorted[i]
    var p   = points[idx]

    //Insert into lower list
    var m = lower.length
    while(m > 1 && orient(
        points[lower[m-2]],
        points[lower[m-1]],
        p) <= 0) {
      m -= 1
      lower.pop()
    }
    lower.push(idx)

    //Insert into upper list
    m = upper.length
    while(m > 1 && orient(
        points[upper[m-2]],
        points[upper[m-1]],
        p) >= 0) {
      m -= 1
      upper.pop()
    }
    upper.push(idx)
  }

  //Merge lists together
  var result = new Array(upper.length + lower.length - 2)
  var ptr    = 0
  for(var i=0, nl=lower.length; i<nl; ++i) {
    result[ptr++] = lower[i]
  }
  for(var j=upper.length-2; j>0; --j) {
    result[ptr++] = upper[j]
  }

  //Return result
  return result
}
},{"robust-orientation":45}],45:[function(require,module,exports){
"use strict"

var twoProduct = require("two-product")
var robustSum = require("robust-sum")
var robustScale = require("robust-scale")
var robustSubtract = require("robust-subtract")

var NUM_EXPAND = 5

var EPSILON     = 1.1102230246251565e-16
var ERRBOUND3   = (3.0 + 16.0 * EPSILON) * EPSILON
var ERRBOUND4   = (7.0 + 56.0 * EPSILON) * EPSILON

function cofactor(m, c) {
  var result = new Array(m.length-1)
  for(var i=1; i<m.length; ++i) {
    var r = result[i-1] = new Array(m.length-1)
    for(var j=0,k=0; j<m.length; ++j) {
      if(j === c) {
        continue
      }
      r[k++] = m[i][j]
    }
  }
  return result
}

function matrix(n) {
  var result = new Array(n)
  for(var i=0; i<n; ++i) {
    result[i] = new Array(n)
    for(var j=0; j<n; ++j) {
      result[i][j] = ["m", j, "[", (n-i-1), "]"].join("")
    }
  }
  return result
}

function sign(n) {
  if(n & 1) {
    return "-"
  }
  return ""
}

function generateSum(expr) {
  if(expr.length === 1) {
    return expr[0]
  } else if(expr.length === 2) {
    return ["sum(", expr[0], ",", expr[1], ")"].join("")
  } else {
    var m = expr.length>>1
    return ["sum(", generateSum(expr.slice(0, m)), ",", generateSum(expr.slice(m)), ")"].join("")
  }
}

function determinant(m) {
  if(m.length === 2) {
    return [["sum(prod(", m[0][0], ",", m[1][1], "),prod(-", m[0][1], ",", m[1][0], "))"].join("")]
  } else {
    var expr = []
    for(var i=0; i<m.length; ++i) {
      expr.push(["scale(", generateSum(determinant(cofactor(m, i))), ",", sign(i), m[0][i], ")"].join(""))
    }
    return expr
  }
}

function orientation(n) {
  var pos = []
  var neg = []
  var m = matrix(n)
  var args = []
  for(var i=0; i<n; ++i) {
    if((i&1)===0) {
      pos.push.apply(pos, determinant(cofactor(m, i)))
    } else {
      neg.push.apply(neg, determinant(cofactor(m, i)))
    }
    args.push("m" + i)
  }
  var posExpr = generateSum(pos)
  var negExpr = generateSum(neg)
  var funcName = "orientation" + n + "Exact"
  var code = ["function ", funcName, "(", args.join(), "){var p=", posExpr, ",n=", negExpr, ",d=sub(p,n);\
return d[d.length-1];};return ", funcName].join("")
  var proc = new Function("sum", "prod", "scale", "sub", code)
  return proc(robustSum, twoProduct, robustScale, robustSubtract)
}

var orientation3Exact = orientation(3)
var orientation4Exact = orientation(4)

var CACHED = [
  function orientation0() { return 0 },
  function orientation1() { return 0 },
  function orientation2(a, b) {
    return b[0] - a[0]
  },
  function orientation3(a, b, c) {
    var l = (a[1] - c[1]) * (b[0] - c[0])
    var r = (a[0] - c[0]) * (b[1] - c[1])
    var det = l - r
    var s
    if(l > 0) {
      if(r <= 0) {
        return det
      } else {
        s = l + r
      }
    } else if(l < 0) {
      if(r >= 0) {
        return det
      } else {
        s = -(l + r)
      }
    } else {
      return det
    }
    var tol = ERRBOUND3 * s
    if(det >= tol || det <= -tol) {
      return det
    }
    return orientation3Exact(a, b, c)
  },
  function orientation4(a,b,c,d) {
    var adx = a[0] - d[0]
    var bdx = b[0] - d[0]
    var cdx = c[0] - d[0]
    var ady = a[1] - d[1]
    var bdy = b[1] - d[1]
    var cdy = c[1] - d[1]
    var adz = a[2] - d[2]
    var bdz = b[2] - d[2]
    var cdz = c[2] - d[2]
    var bdxcdy = bdx * cdy
    var cdxbdy = cdx * bdy
    var cdxady = cdx * ady
    var adxcdy = adx * cdy
    var adxbdy = adx * bdy
    var bdxady = bdx * ady
    var det = adz * (bdxcdy - cdxbdy)
            + bdz * (cdxady - adxcdy)
            + cdz * (adxbdy - bdxady)
    var permanent = (Math.abs(bdxcdy) + Math.abs(cdxbdy)) * Math.abs(adz)
                  + (Math.abs(cdxady) + Math.abs(adxcdy)) * Math.abs(bdz)
                  + (Math.abs(adxbdy) + Math.abs(bdxady)) * Math.abs(cdz)
    var tol = ERRBOUND4 * permanent
    if ((det > tol) || (-det > tol)) {
      return det
    }
    return orientation4Exact(a,b,c,d)
  }
]

function slowOrient(args) {
  var proc = CACHED[args.length]
  if(!proc) {
    proc = CACHED[args.length] = orientation(args.length)
  }
  return proc.apply(undefined, args)
}

function generateOrientationProc() {
  while(CACHED.length <= NUM_EXPAND) {
    CACHED.push(orientation(CACHED.length))
  }
  var args = []
  var procArgs = ["slow"]
  for(var i=0; i<=NUM_EXPAND; ++i) {
    args.push("a" + i)
    procArgs.push("o" + i)
  }
  var code = [
    "function getOrientation(", args.join(), "){switch(arguments.length){case 0:case 1:return 0;"
  ]
  for(var i=2; i<=NUM_EXPAND; ++i) {
    code.push("case ", i, ":return o", i, "(", args.slice(0, i).join(), ");")
  }
  code.push("}var s=new Array(arguments.length);for(var i=0;i<arguments.length;++i){s[i]=arguments[i]};return slow(s);}return getOrientation")
  procArgs.push(code.join(""))

  var proc = Function.apply(undefined, procArgs)
  module.exports = proc.apply(undefined, [slowOrient].concat(CACHED))
  for(var i=0; i<=NUM_EXPAND; ++i) {
    module.exports[i] = CACHED[i]
  }
}

generateOrientationProc()
},{"robust-scale":46,"robust-subtract":47,"robust-sum":48,"two-product":52}],46:[function(require,module,exports){
"use strict"

var twoProduct = require("two-product")
var twoSum = require("two-sum")

module.exports = scaleLinearExpansion

function scaleLinearExpansion(e, scale) {
  var n = e.length
  if(n === 1) {
    var ts = twoProduct(e[0], scale)
    if(ts[0]) {
      return ts
    }
    return [ ts[1] ]
  }
  var g = new Array(2 * n)
  var q = [0.1, 0.1]
  var t = [0.1, 0.1]
  var count = 0
  twoProduct(e[0], scale, q)
  if(q[0]) {
    g[count++] = q[0]
  }
  for(var i=1; i<n; ++i) {
    twoProduct(e[i], scale, t)
    var pq = q[1]
    twoSum(pq, t[0], q)
    if(q[0]) {
      g[count++] = q[0]
    }
    var a = t[1]
    var b = q[1]
    var x = a + b
    var bv = x - a
    var y = b - bv
    q[1] = x
    if(y) {
      g[count++] = y
    }
  }
  if(q[1]) {
    g[count++] = q[1]
  }
  if(count === 0) {
    g[count++] = 0.0
  }
  g.length = count
  return g
}
},{"two-product":52,"two-sum":53}],47:[function(require,module,exports){
"use strict"

module.exports = robustSubtract

//Easy case: Add two scalars
function scalarScalar(a, b) {
  var x = a + b
  var bv = x - a
  var av = x - bv
  var br = b - bv
  var ar = a - av
  var y = ar + br
  if(y) {
    return [y, x]
  }
  return [x]
}

function robustSubtract(e, f) {
  var ne = e.length|0
  var nf = f.length|0
  if(ne === 1 && nf === 1) {
    return scalarScalar(e[0], -f[0])
  }
  var n = ne + nf
  var g = new Array(n)
  var count = 0
  var eptr = 0
  var fptr = 0
  var abs = Math.abs
  var ei = e[eptr]
  var ea = abs(ei)
  var fi = -f[fptr]
  var fa = abs(fi)
  var a, b
  if(ea < fa) {
    b = ei
    eptr += 1
    if(eptr < ne) {
      ei = e[eptr]
      ea = abs(ei)
    }
  } else {
    b = fi
    fptr += 1
    if(fptr < nf) {
      fi = -f[fptr]
      fa = abs(fi)
    }
  }
  if((eptr < ne && ea < fa) || (fptr >= nf)) {
    a = ei
    eptr += 1
    if(eptr < ne) {
      ei = e[eptr]
      ea = abs(ei)
    }
  } else {
    a = fi
    fptr += 1
    if(fptr < nf) {
      fi = -f[fptr]
      fa = abs(fi)
    }
  }
  var x = a + b
  var bv = x - a
  var y = b - bv
  var q0 = y
  var q1 = x
  var _x, _bv, _av, _br, _ar
  while(eptr < ne && fptr < nf) {
    if(ea < fa) {
      a = ei
      eptr += 1
      if(eptr < ne) {
        ei = e[eptr]
        ea = abs(ei)
      }
    } else {
      a = fi
      fptr += 1
      if(fptr < nf) {
        fi = -f[fptr]
        fa = abs(fi)
      }
    }
    b = q0
    x = a + b
    bv = x - a
    y = b - bv
    if(y) {
      g[count++] = y
    }
    _x = q1 + x
    _bv = _x - q1
    _av = _x - _bv
    _br = x - _bv
    _ar = q1 - _av
    q0 = _ar + _br
    q1 = _x
  }
  while(eptr < ne) {
    a = ei
    b = q0
    x = a + b
    bv = x - a
    y = b - bv
    if(y) {
      g[count++] = y
    }
    _x = q1 + x
    _bv = _x - q1
    _av = _x - _bv
    _br = x - _bv
    _ar = q1 - _av
    q0 = _ar + _br
    q1 = _x
    eptr += 1
    if(eptr < ne) {
      ei = e[eptr]
    }
  }
  while(fptr < nf) {
    a = fi
    b = q0
    x = a + b
    bv = x - a
    y = b - bv
    if(y) {
      g[count++] = y
    }
    _x = q1 + x
    _bv = _x - q1
    _av = _x - _bv
    _br = x - _bv
    _ar = q1 - _av
    q0 = _ar + _br
    q1 = _x
    fptr += 1
    if(fptr < nf) {
      fi = -f[fptr]
    }
  }
  if(q0) {
    g[count++] = q0
  }
  if(q1) {
    g[count++] = q1
  }
  if(!count) {
    g[count++] = 0.0
  }
  g.length = count
  return g
}
},{}],48:[function(require,module,exports){
"use strict"

module.exports = linearExpansionSum

//Easy case: Add two scalars
function scalarScalar(a, b) {
  var x = a + b
  var bv = x - a
  var av = x - bv
  var br = b - bv
  var ar = a - av
  var y = ar + br
  if(y) {
    return [y, x]
  }
  return [x]
}

function linearExpansionSum(e, f) {
  var ne = e.length|0
  var nf = f.length|0
  if(ne === 1 && nf === 1) {
    return scalarScalar(e[0], f[0])
  }
  var n = ne + nf
  var g = new Array(n)
  var count = 0
  var eptr = 0
  var fptr = 0
  var abs = Math.abs
  var ei = e[eptr]
  var ea = abs(ei)
  var fi = f[fptr]
  var fa = abs(fi)
  var a, b
  if(ea < fa) {
    b = ei
    eptr += 1
    if(eptr < ne) {
      ei = e[eptr]
      ea = abs(ei)
    }
  } else {
    b = fi
    fptr += 1
    if(fptr < nf) {
      fi = f[fptr]
      fa = abs(fi)
    }
  }
  if((eptr < ne && ea < fa) || (fptr >= nf)) {
    a = ei
    eptr += 1
    if(eptr < ne) {
      ei = e[eptr]
      ea = abs(ei)
    }
  } else {
    a = fi
    fptr += 1
    if(fptr < nf) {
      fi = f[fptr]
      fa = abs(fi)
    }
  }
  var x = a + b
  var bv = x - a
  var y = b - bv
  var q0 = y
  var q1 = x
  var _x, _bv, _av, _br, _ar
  while(eptr < ne && fptr < nf) {
    if(ea < fa) {
      a = ei
      eptr += 1
      if(eptr < ne) {
        ei = e[eptr]
        ea = abs(ei)
      }
    } else {
      a = fi
      fptr += 1
      if(fptr < nf) {
        fi = f[fptr]
        fa = abs(fi)
      }
    }
    b = q0
    x = a + b
    bv = x - a
    y = b - bv
    if(y) {
      g[count++] = y
    }
    _x = q1 + x
    _bv = _x - q1
    _av = _x - _bv
    _br = x - _bv
    _ar = q1 - _av
    q0 = _ar + _br
    q1 = _x
  }
  while(eptr < ne) {
    a = ei
    b = q0
    x = a + b
    bv = x - a
    y = b - bv
    if(y) {
      g[count++] = y
    }
    _x = q1 + x
    _bv = _x - q1
    _av = _x - _bv
    _br = x - _bv
    _ar = q1 - _av
    q0 = _ar + _br
    q1 = _x
    eptr += 1
    if(eptr < ne) {
      ei = e[eptr]
    }
  }
  while(fptr < nf) {
    a = fi
    b = q0
    x = a + b
    bv = x - a
    y = b - bv
    if(y) {
      g[count++] = y
    }
    _x = q1 + x
    _bv = _x - q1
    _av = _x - _bv
    _br = x - _bv
    _ar = q1 - _av
    q0 = _ar + _br
    q1 = _x
    fptr += 1
    if(fptr < nf) {
      fi = f[fptr]
    }
  }
  if(q0) {
    g[count++] = q0
  }
  if(q1) {
    g[count++] = q1
  }
  if(!count) {
    g[count++] = 0.0
  }
  g.length = count
  return g
}
},{}],49:[function(require,module,exports){
"use strict"; "use restrict";

var bits      = require("bit-twiddle")
  , UnionFind = require("union-find")

//Returns the dimension of a cell complex
function dimension(cells) {
  var d = 0
    , max = Math.max
  for(var i=0, il=cells.length; i<il; ++i) {
    d = max(d, cells[i].length)
  }
  return d-1
}
exports.dimension = dimension

//Counts the number of vertices in faces
function countVertices(cells) {
  var vc = -1
    , max = Math.max
  for(var i=0, il=cells.length; i<il; ++i) {
    var c = cells[i]
    for(var j=0, jl=c.length; j<jl; ++j) {
      vc = max(vc, c[j])
    }
  }
  return vc+1
}
exports.countVertices = countVertices

//Returns a deep copy of cells
function cloneCells(cells) {
  var ncells = new Array(cells.length)
  for(var i=0, il=cells.length; i<il; ++i) {
    ncells[i] = cells[i].slice(0)
  }
  return ncells
}
exports.cloneCells = cloneCells

//Ranks a pair of cells up to permutation
function compareCells(a, b) {
  var n = a.length
    , t = a.length - b.length
    , min = Math.min
  if(t) {
    return t
  }
  switch(n) {
    case 0:
      return 0;
    case 1:
      return a[0] - b[0];
    case 2:
      var d = a[0]+a[1]-b[0]-b[1]
      if(d) {
        return d
      }
      return min(a[0],a[1]) - min(b[0],b[1])
    case 3:
      var l1 = a[0]+a[1]
        , m1 = b[0]+b[1]
      d = l1+a[2] - (m1+b[2])
      if(d) {
        return d
      }
      var l0 = min(a[0], a[1])
        , m0 = min(b[0], b[1])
        , d  = min(l0, a[2]) - min(m0, b[2])
      if(d) {
        return d
      }
      return min(l0+a[2], l1) - min(m0+b[2], m1)

    //TODO: Maybe optimize n=4 as well?

    default:
      var as = a.slice(0)
      as.sort()
      var bs = b.slice(0)
      bs.sort()
      for(var i=0; i<n; ++i) {
        t = as[i] - bs[i]
        if(t) {
          return t
        }
      }
      return 0
  }
}
exports.compareCells = compareCells

function compareZipped(a, b) {
  return compareCells(a[0], b[0])
}

//Puts a cell complex into normal order for the purposes of findCell queries
function normalize(cells, attr) {
  if(attr) {
    var len = cells.length
    var zipped = new Array(len)
    for(var i=0; i<len; ++i) {
      zipped[i] = [cells[i], attr[i]]
    }
    zipped.sort(compareZipped)
    for(var i=0; i<len; ++i) {
      cells[i] = zipped[i][0]
      attr[i] = zipped[i][1]
    }
    return cells
  } else {
    cells.sort(compareCells)
    return cells
  }
}
exports.normalize = normalize

//Removes all duplicate cells in the complex
function unique(cells) {
  if(cells.length === 0) {
    return []
  }
  var ptr = 1
    , len = cells.length
  for(var i=1; i<len; ++i) {
    var a = cells[i]
    if(compareCells(a, cells[i-1])) {
      if(i === ptr) {
        ptr++
        continue
      }
      cells[ptr++] = a
    }
  }
  cells.length = ptr
  return cells
}
exports.unique = unique;

//Finds a cell in a normalized cell complex
function findCell(cells, c) {
  var lo = 0
    , hi = cells.length-1
    , r  = -1
  while (lo <= hi) {
    var mid = (lo + hi) >> 1
      , s   = compareCells(cells[mid], c)
    if(s <= 0) {
      if(s === 0) {
        r = mid
      }
      lo = mid + 1
    } else if(s > 0) {
      hi = mid - 1
    }
  }
  return r
}
exports.findCell = findCell;

//Builds an index for an n-cell.  This is more general than dual, but less efficient
function incidence(from_cells, to_cells) {
  var index = new Array(from_cells.length)
  for(var i=0, il=index.length; i<il; ++i) {
    index[i] = []
  }
  var b = []
  for(var i=0, n=to_cells.length; i<n; ++i) {
    var c = to_cells[i]
    var cl = c.length
    for(var k=1, kn=(1<<cl); k<kn; ++k) {
      b.length = bits.popCount(k)
      var l = 0
      for(var j=0; j<cl; ++j) {
        if(k & (1<<j)) {
          b[l++] = c[j]
        }
      }
      var idx=findCell(from_cells, b)
      if(idx < 0) {
        continue
      }
      while(true) {
        index[idx++].push(i)
        if(idx >= from_cells.length || compareCells(from_cells[idx], b) !== 0) {
          break
        }
      }
    }
  }
  return index
}
exports.incidence = incidence

//Computes the dual of the mesh.  This is basically an optimized version of buildIndex for the situation where from_cells is just the list of vertices
function dual(cells, vertex_count) {
  if(!vertex_count) {
    return incidence(unique(skeleton(cells, 0)), cells, 0)
  }
  var res = new Array(vertex_count)
  for(var i=0; i<vertex_count; ++i) {
    res[i] = []
  }
  for(var i=0, len=cells.length; i<len; ++i) {
    var c = cells[i]
    for(var j=0, cl=c.length; j<cl; ++j) {
      res[c[j]].push(i)
    }
  }
  return res
}
exports.dual = dual

//Enumerates all cells in the complex
function explode(cells) {
  var result = []
  for(var i=0, il=cells.length; i<il; ++i) {
    var c = cells[i]
      , cl = c.length|0
    for(var j=1, jl=(1<<cl); j<jl; ++j) {
      var b = []
      for(var k=0; k<cl; ++k) {
        if((j >>> k) & 1) {
          b.push(c[k])
        }
      }
      result.push(b)
    }
  }
  return normalize(result)
}
exports.explode = explode

//Enumerates all of the n-cells of a cell complex
function skeleton(cells, n) {
  if(n < 0) {
    return []
  }
  var result = []
    , k0     = (1<<(n+1))-1
  for(var i=0; i<cells.length; ++i) {
    var c = cells[i]
    for(var k=k0; k<(1<<c.length); k=bits.nextCombination(k)) {
      var b = new Array(n+1)
        , l = 0
      for(var j=0; j<c.length; ++j) {
        if(k & (1<<j)) {
          b[l++] = c[j]
        }
      }
      result.push(b)
    }
  }
  return normalize(result)
}
exports.skeleton = skeleton;

//Computes the boundary of all cells, does not remove duplicates
function boundary(cells) {
  var res = []
  for(var i=0,il=cells.length; i<il; ++i) {
    var c = cells[i]
    for(var j=0,cl=c.length; j<cl; ++j) {
      var b = new Array(c.length-1)
      for(var k=0, l=0; k<cl; ++k) {
        if(k !== j) {
          b[l++] = c[k]
        }
      }
      res.push(b)
    }
  }
  return normalize(res)
}
exports.boundary = boundary;

//Computes connected components for a dense cell complex
function connectedComponents_dense(cells, vertex_count) {
  var labels = new UnionFind(vertex_count)
  for(var i=0; i<cells.length; ++i) {
    var c = cells[i]
    for(var j=0; j<c.length; ++j) {
      for(var k=j+1; k<c.length; ++k) {
        labels.link(c[j], c[k])
      }
    }
  }
  var components = []
    , component_labels = labels.ranks
  for(var i=0; i<component_labels.length; ++i) {
    component_labels[i] = -1
  }
  for(var i=0; i<cells.length; ++i) {
    var l = labels.find(cells[i][0])
    if(component_labels[l] < 0) {
      component_labels[l] = components.length
      components.push([cells[i].slice(0)])
    } else {
      components[component_labels[l]].push(cells[i].slice(0))
    }
  }
  return components
}

//Computes connected components for a sparse graph
function connectedComponents_sparse(cells) {
  var vertices  = unique(normalize(skeleton(cells, 0)))
    , labels    = new UnionFind(vertices.length)
  for(var i=0; i<cells.length; ++i) {
    var c = cells[i]
    for(var j=0; j<c.length; ++j) {
      var vj = findCell(vertices, [c[j]])
      for(var k=j+1; k<c.length; ++k) {
        labels.link(vj, findCell(vertices, [c[k]]))
      }
    }
  }
  var components        = []
    , component_labels  = labels.ranks
  for(var i=0; i<component_labels.length; ++i) {
    component_labels[i] = -1
  }
  for(var i=0; i<cells.length; ++i) {
    var l = labels.find(findCell(vertices, [cells[i][0]]));
    if(component_labels[l] < 0) {
      component_labels[l] = components.length
      components.push([cells[i].slice(0)])
    } else {
      components[component_labels[l]].push(cells[i].slice(0))
    }
  }
  return components
}

//Computes connected components for a cell complex
function connectedComponents(cells, vertex_count) {
  if(vertex_count) {
    return connectedComponents_dense(cells, vertex_count)
  }
  return connectedComponents_sparse(cells)
}
exports.connectedComponents = connectedComponents

},{"bit-twiddle":38,"union-find":54}],50:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],51:[function(require,module,exports){
arguments[4][12][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-meta":98,"dup":12}],52:[function(require,module,exports){
"use strict"

module.exports = twoProduct

var SPLITTER = +(Math.pow(2, 27) + 1.0)

function twoProduct(a, b, result) {
  var x = a * b

  var c = SPLITTER * a
  var abig = c - a
  var ahi = c - abig
  var alo = a - ahi

  var d = SPLITTER * b
  var bbig = d - b
  var bhi = d - bbig
  var blo = b - bhi

  var err1 = x - (ahi * bhi)
  var err2 = err1 - (alo * bhi)
  var err3 = err2 - (ahi * blo)

  var y = alo * blo - err3

  if(result) {
    result[0] = y
    result[1] = x
    return result
  }

  return [ y, x ]
}
},{}],53:[function(require,module,exports){
"use strict"

module.exports = fastTwoSum

function fastTwoSum(a, b, result) {
	var x = a + b
	var bv = x - a
	var av = x - bv
	var br = b - bv
	var ar = a - av
	if(result) {
		result[0] = ar + br
		result[1] = x
		return result
	}
	return [ar+br, x]
}
},{}],54:[function(require,module,exports){
"use strict"; "use restrict";

module.exports = UnionFind;

function UnionFind(count) {
  this.roots = new Array(count);
  this.ranks = new Array(count);

  for(var i=0; i<count; ++i) {
    this.roots[i] = i;
    this.ranks[i] = 0;
  }
}

var proto = UnionFind.prototype

Object.defineProperty(proto, "length", {
  "get": function() {
    return this.roots.length
  }
})

proto.makeSet = function() {
  var n = this.roots.length;
  this.roots.push(n);
  this.ranks.push(0);
  return n;
}

proto.find = function(x) {
  var x0 = x
  var roots = this.roots;
  while(roots[x] !== x) {
    x = roots[x]
  }
  while(roots[x0] !== x) {
    var y = roots[x0]
    roots[x0] = x
    x0 = y
  }
  return x;
}

proto.link = function(x, y) {
  var xr = this.find(x)
    , yr = this.find(y);
  if(xr === yr) {
    return;
  }
  var ranks = this.ranks
    , roots = this.roots
    , xd    = ranks[xr]
    , yd    = ranks[yr];
  if(xd < yd) {
    roots[xr] = yr;
  } else if(yd < xd) {
    roots[yr] = xr;
  } else {
    roots[yr] = xr;
    ++ranks[xr];
  }
}
},{}],55:[function(require,module,exports){
//http://en.wikipedia.org/wiki/Haversine_formula
//http://www.movable-type.co.uk/scripts-cadastro-imovel/latlong.html
var getCoord = require('turf-invariant').getCoord;
var helpers = require('turf-helpers');
var point = helpers.point;
var distanceToRadians = helpers.distanceToRadians;

/**
 * Takes a {@link Point} and calculates the location of a destination point given a distance in degrees, radians, miles, or kilometers; and bearing in degrees. This uses the [Haversine formula](http://en.wikipedia.org/wiki/Haversine_formula) to account for global curvature.
 *
 * @name destination
 * @param {Feature<Point>} from starting point
 * @param {number} distance distance from the starting point
 * @param {number} bearing ranging from -180 to 180
 * @param {String} [units=kilometers] miles, kilometers, degrees, or radians
 * @returns {Feature<Point>} destination point
 * @example
 * var point = {
 *   "type": "Feature",
 *   "properties": {
 *     "marker-color": "#0f0"
 *   },
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-75.343, 39.984]
 *   }
 * };
 * var distance = 50;
 * var bearing = 90;
 * var units = 'miles';
 *
 * var destination = turf.destination(point, distance, bearing, units);
 * destination.properties['marker-color'] = '#f00';
 *
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": [point, destination]
 * };
 *
 * //=result
 */
module.exports = function (from, distance, bearing, units) {
    var degrees2radians = Math.PI / 180;
    var radians2degrees = 180 / Math.PI;
    var coordinates1 = getCoord(from);
    var longitude1 = degrees2radians * coordinates1[0];
    var latitude1 = degrees2radians * coordinates1[1];
    var bearing_rad = degrees2radians * bearing;

    var radians = distanceToRadians(distance, units);

    var latitude2 = Math.asin(Math.sin(latitude1) * Math.cos(radians) +
        Math.cos(latitude1) * Math.sin(radians) * Math.cos(bearing_rad));
    var longitude2 = longitude1 + Math.atan2(Math.sin(bearing_rad) *
        Math.sin(radians) * Math.cos(latitude1),
        Math.cos(radians) - Math.sin(latitude1) * Math.sin(latitude2));

    return point([radians2degrees * longitude2, radians2degrees * latitude2]);
};

},{"turf-helpers":56,"turf-invariant":57}],56:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],57:[function(require,module,exports){
arguments[4][14][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-invariant":79,"dup":14}],58:[function(require,module,exports){
// depend on jsts for now https://github.com/bjornharrtell/jsts/blob/master/examples/overlay.html
var jsts = require('jsts');

/**
 * Finds the difference between two {@link Polygon|polygons} by clipping the second
 * polygon from the first.
 *
 * @name difference
 * @param {Feature<Polygon>} poly1 input Polygon feaure
 * @param {Feature<Polygon>} poly2 Polygon feature to difference from `poly1`
 * @return {Feature<Polygon>} a Polygon feature showing the area of `poly1` excluding the area of `poly2`
 * @example
 * var poly1 = {
 *   "type": "Feature",
 *   "properties": {
 *     "fill": "#0f0"
 *   },
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [-46.738586, -23.596711],
 *       [-46.738586, -23.458207],
 *       [-46.560058, -23.458207],
 *       [-46.560058, -23.596711],
 *       [-46.738586, -23.596711]
 *     ]]
 *   }
 * };
 * var poly2 = {
 *   "type": "Feature",
 *   "properties": {
 *     "fill": "#00f"
 *   },
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [-46.650009, -23.631314],
 *       [-46.650009, -23.5237],
 *       [-46.509246, -23.5237],
 *       [-46.509246, -23.631314],
 *       [-46.650009, -23.631314]
 *     ]]
 *   }
 * };
 *
 * var differenced = turf.difference(poly1, poly2);
 * differenced.properties.fill = '#f00';
 *
 * var polygons = {
 *   "type": "FeatureCollection",
 *   "features": [poly1, poly2]
 * };
 *
 * //=polygons
 *
 * //=differenced
 */

module.exports = function (p1, p2) {
    var poly1 = JSON.parse(JSON.stringify(p1));
    var poly2 = JSON.parse(JSON.stringify(p2));
    if (poly1.type !== 'Feature') {
        poly1 = {
            type: 'Feature',
            properties: {},
            geometry: poly1
        };
    }
    if (poly2.type !== 'Feature') {
        poly2 = {
            type: 'Feature',
            properties: {},
            geometry: poly2
        };
    }

    var reader = new jsts.io.GeoJSONReader();
    var a = reader.read(JSON.stringify(poly1.geometry));
    var b = reader.read(JSON.stringify(poly2.geometry));
    var differenced = a.difference(b);

    if (differenced.isEmpty()) return undefined;

    var writer = new jsts.io.GeoJSONWriter();
    var geojsonGeometry = writer.write(differenced);

    poly1.geometry = differenced;

    return {
        type: 'Feature',
        properties: poly1.properties,
        geometry: geojsonGeometry
    };
};

},{"jsts":59}],59:[function(require,module,exports){
arguments[4][20][0].apply(exports,arguments)
},{"dup":20}],60:[function(require,module,exports){
var getCoord = require('turf-invariant').getCoord;
var radiansToDistance = require('turf-helpers').radiansToDistance;
//http://en.wikipedia.org/wiki/Haversine_formula
//http://www.movable-type.co.uk/scripts-cadastro-imovel/latlong.html

/**
 * Calculates the distance between two {@link Point|points} in degrees, radians,
 * miles, or kilometers. This uses the
 * [Haversine formula](http://en.wikipedia.org/wiki/Haversine_formula)
 * to account for global curvature.
 *
 * @name distance
 * @param {Feature<Point>} from origin point
 * @param {Feature<Point>} to destination point
 * @param {String} [units=kilometers] can be degrees, radians, miles, or kilometers
 * @return {Number} distance between the two points
 * @example
 * var from = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-75.343, 39.984]
 *   }
 * };
 * var to = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-75.534, 39.123]
 *   }
 * };
 * var units = "miles";
 *
 * var points = {
 *   "type": "FeatureCollection",
 *   "features": [from, to]
 * };
 *
 * //=points
 *
 * var distance = turf.distance(from, to, units);
 *
 * //=distance
 */
module.exports = function (from, to, units) {
    var degrees2radians = Math.PI / 180;
    var coordinates1 = getCoord(from);
    var coordinates2 = getCoord(to);
    var dLat = degrees2radians * (coordinates2[1] - coordinates1[1]);
    var dLon = degrees2radians * (coordinates2[0] - coordinates1[0]);
    var lat1 = degrees2radians * coordinates1[1];
    var lat2 = degrees2radians * coordinates2[1];

    var a = Math.pow(Math.sin(dLat / 2), 2) +
          Math.pow(Math.sin(dLon / 2), 2) * Math.cos(lat1) * Math.cos(lat2);

    return radiansToDistance(2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)), units);
};

},{"turf-helpers":61,"turf-invariant":62}],61:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],62:[function(require,module,exports){
arguments[4][14][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-invariant":79,"dup":14}],63:[function(require,module,exports){
var bbox = require('turf-bbox');
var bboxPolygon = require('turf-bbox-polygon');

/**
 * Takes any number of features and returns a rectangular {@link Polygon} that encompasses all vertices.
 *
 * @name envelope
 * @param {FeatureCollection} fc input features
 * @return {Feature<Polygon>} a rectangular Polygon feature that encompasses all vertices
 * @example
 * var fc = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     {
 *       "type": "Feature",
 *       "properties": {
 *         "name": "Location A"
 *       },
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-75.343, 39.984]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {
 *         "name": "Location B"
 *       },
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-75.833, 39.284]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {
 *         "name": "Location C"
 *       },
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-75.534, 39.123]
 *       }
 *     }
 *   ]
 * };
 *
 * var enveloped = turf.envelope(fc);
 *
 * var resultFeatures = fc.features.concat(enveloped);
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": resultFeatures
 * };
 *
 * //=result
 */

module.exports = function (features) {
    return bboxPolygon(bbox(features));
};

},{"turf-bbox":65,"turf-bbox-polygon":64}],64:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-bbox-polygon");

},{"/Users/tmcw/src/turf/packages/turf-bbox-polygon":9}],65:[function(require,module,exports){
arguments[4][23][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-bbox":11,"dup":23}],66:[function(require,module,exports){
var featureCollection = require('turf-helpers').featureCollection;
var each = require('turf-meta').coordEach;
var point = require('turf-helpers').point;

/**
 * Takes a feature or set of features and returns all positions as
 * {@link Point|points}.
 *
 * @name explode
 * @param {(Feature|FeatureCollection)} geojson input features
 * @return {FeatureCollection<point>} points representing the exploded input features
 * @throws {Error} if it encounters an unknown geometry type
 * @example
 * var poly = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [177.434692, -17.77517],
 *       [177.402076, -17.779093],
 *       [177.38079, -17.803937],
 *       [177.40242, -17.826164],
 *       [177.438468, -17.824857],
 *       [177.454948, -17.796746],
 *       [177.434692, -17.77517]
 *     ]]
 *   }
 * };
 *
 * var points = turf.explode(poly);
 *
 * //=poly
 *
 * //=points
 */
module.exports = function (geojson) {
    var points = [];
    each(geojson, function (coord) {
        points.push(point(coord));
    });
    return featureCollection(points);
};

},{"turf-helpers":67,"turf-meta":68}],67:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],68:[function(require,module,exports){
arguments[4][12][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-meta":98,"dup":12}],69:[function(require,module,exports){
var coordEach = require('turf-meta').coordEach;

/**
 * Takes input features and flips all of their coordinates
 * from `[x, y]` to `[y, x]`.
 *
 * @name flip
 * @param {(Feature|FeatureCollection)} input input features
 * @returns {(Feature|FeatureCollection)} a feature or set of features of the same type as `input` with flipped coordinates
 * @example
 * var serbia = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [20.566406, 43.421008]
 *   }
 * };
 *
 * //=serbia
 *
 * var saudiArabia = turf.flip(serbia);
 *
 * //=saudiArabia
 */
module.exports = function flip(input) {
    // ensure that we don't modify features in-place and changes to the
    // output do not change the previous feature, including changes to nested
    // properties.
    input = JSON.parse(JSON.stringify(input));

    coordEach(input, function (coord) {
        coord.reverse();
    });
    return input;
};

},{"turf-meta":70}],70:[function(require,module,exports){
arguments[4][12][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-meta":98,"dup":12}],71:[function(require,module,exports){
/**
 * Wraps a GeoJSON {@link Geometry} in a GeoJSON {@link Feature}.
 *
 * @name feature
 * @param {Geometry} geometry input geometry
 * @param {Object} properties properties
 * @returns {FeatureCollection} a FeatureCollection of input features
 * @example
 * var geometry = {
 *      "type": "Point",
 *      "coordinates": [
 *        67.5,
 *        32.84267363195431
 *      ]
 *    }
 *
 * var feature = turf.feature(geometry);
 *
 * //=feature
 */
function feature(geometry, properties) {
    return {
        type: 'Feature',
        properties: properties || {},
        geometry: geometry
    };
}

module.exports.feature = feature;

/**
 * Takes coordinates and properties (optional) and returns a new {@link Point} feature.
 *
 * @name point
 * @param {number[]} coordinates longitude, latitude position (each in decimal degrees)
 * @param {Object=} properties an Object that is used as the {@link Feature}'s
 * properties
 * @returns {Feature<Point>} a Point feature
 * @example
 * var pt1 = turf.point([-75.343, 39.984]);
 *
 * //=pt1
 */
module.exports.point = function (coordinates, properties) {
    if (!Array.isArray(coordinates)) throw new Error('Coordinates must be an array');
    if (coordinates.length < 2) throw new Error('Coordinates must be at least 2 numbers long');
    return feature({
        type: 'Point',
        coordinates: coordinates.slice()
    }, properties);
};

/**
 * Takes an array of LinearRings and optionally an {@link Object} with properties and returns a {@link Polygon} feature.
 *
 * @name polygon
 * @param {Array<Array<Array<number>>>} coordinates an array of LinearRings
 * @param {Object=} properties a properties object
 * @returns {Feature<Polygon>} a Polygon feature
 * @throws {Error} throw an error if a LinearRing of the polygon has too few positions
 * or if a LinearRing of the Polygon does not have matching Positions at the
 * beginning & end.
 * @example
 * var polygon = turf.polygon([[
 *  [-2.275543, 53.464547],
 *  [-2.275543, 53.489271],
 *  [-2.215118, 53.489271],
 *  [-2.215118, 53.464547],
 *  [-2.275543, 53.464547]
 * ]], { name: 'poly1', population: 400});
 *
 * //=polygon
 */
module.exports.polygon = function (coordinates, properties) {

    if (!coordinates) throw new Error('No coordinates passed');

    for (var i = 0; i < coordinates.length; i++) {
        var ring = coordinates[i];
        if (ring.length < 4) {
            throw new Error('Each LinearRing of a Polygon must have 4 or more Positions.');
        }
        for (var j = 0; j < ring[ring.length - 1].length; j++) {
            if (ring[ring.length - 1][j] !== ring[0][j]) {
                throw new Error('First and last Position are not equivalent.');
            }
        }
    }

    return feature({
        type: 'Polygon',
        coordinates: coordinates
    }, properties);
};

/**
 * Creates a {@link LineString} based on a
 * coordinate array. Properties can be added optionally.
 *
 * @name lineString
 * @param {Array<Array<number>>} coordinates an array of Positions
 * @param {Object=} properties an Object of key-value pairs to add as properties
 * @returns {Feature<LineString>} a LineString feature
 * @throws {Error} if no coordinates are passed
 * @example
 * var linestring1 = turf.lineString([
 *	[-21.964416, 64.148203],
 *	[-21.956176, 64.141316],
 *	[-21.93901, 64.135924],
 *	[-21.927337, 64.136673]
 * ]);
 * var linestring2 = turf.lineString([
 *	[-21.929054, 64.127985],
 *	[-21.912918, 64.134726],
 *	[-21.916007, 64.141016],
 * 	[-21.930084, 64.14446]
 * ], {name: 'line 1', distance: 145});
 *
 * //=linestring1
 *
 * //=linestring2
 */
module.exports.lineString = function (coordinates, properties) {
    if (!coordinates) {
        throw new Error('No coordinates passed');
    }
    return feature({
        type: 'LineString',
        coordinates: coordinates
    }, properties);
};

/**
 * Takes one or more {@link Feature|Features} and creates a {@link FeatureCollection}.
 *
 * @name featureCollection
 * @param {Feature[]} features input features
 * @returns {FeatureCollection} a FeatureCollection of input features
 * @example
 * var features = [
 *  turf.point([-75.343, 39.984], {name: 'Location A'}),
 *  turf.point([-75.833, 39.284], {name: 'Location B'}),
 *  turf.point([-75.534, 39.123], {name: 'Location C'})
 * ];
 *
 * var fc = turf.featureCollection(features);
 *
 * //=fc
 */
module.exports.featureCollection = function (features) {
    return {
        type: 'FeatureCollection',
        features: features
    };
};

/**
 * Creates a {@link Feature<MultiLineString>} based on a
 * coordinate array. Properties can be added optionally.
 *
 * @name multiLineString
 * @param {Array<Array<Array<number>>>} coordinates an array of LineStrings
 * @param {Object=} properties an Object of key-value pairs to add as properties
 * @returns {Feature<MultiLineString>} a MultiLineString feature
 * @throws {Error} if no coordinates are passed
 * @example
 * var multiLine = turf.multiLineString([[[0,0],[10,10]]]);
 *
 * //=multiLine
 *
 */
module.exports.multiLineString = function (coordinates, properties) {
    if (!coordinates) {
        throw new Error('No coordinates passed');
    }
    return feature({
        type: 'MultiLineString',
        coordinates: coordinates
    }, properties);
};

/**
 * Creates a {@link Feature<MultiPoint>} based on a
 * coordinate array. Properties can be added optionally.
 *
 * @name multiPoint
 * @param {Array<Array<number>>} coordinates an array of Positions
 * @param {Object=} properties an Object of key-value pairs to add as properties
 * @returns {Feature<MultiPoint>} a MultiPoint feature
 * @throws {Error} if no coordinates are passed
 * @example
 * var multiPt = turf.multiPoint([[0,0],[10,10]]);
 *
 * //=multiPt
 *
 */
module.exports.multiPoint = function (coordinates, properties) {
    if (!coordinates) {
        throw new Error('No coordinates passed');
    }
    return feature({
        type: 'MultiPoint',
        coordinates: coordinates
    }, properties);
};


/**
 * Creates a {@link Feature<MultiPolygon>} based on a
 * coordinate array. Properties can be added optionally.
 *
 * @name multiPolygon
 * @param {Array<Array<Array<Array<number>>>>} coordinates an array of Polygons
 * @param {Object=} properties an Object of key-value pairs to add as properties
 * @returns {Feature<MultiPolygon>} a multipolygon feature
 * @throws {Error} if no coordinates are passed
 * @example
 * var multiPoly = turf.multiPolygon([[[[0,0],[0,10],[10,10],[10,0],[0,0]]]);
 *
 * //=multiPoly
 *
 */
module.exports.multiPolygon = function (coordinates, properties) {
    if (!coordinates) {
        throw new Error('No coordinates passed');
    }
    return feature({
        type: 'MultiPolygon',
        coordinates: coordinates
    }, properties);
};

/**
 * Creates a {@link Feature<GeometryCollection>} based on a
 * coordinate array. Properties can be added optionally.
 *
 * @name geometryCollection
 * @param {Array<{Geometry}>} geometries an array of GeoJSON Geometries
 * @param {Object=} properties an Object of key-value pairs to add as properties
 * @returns {Feature<GeometryCollection>} a geometrycollection feature
 * @example
 * var pt = {
 *     "type": "Point",
 *       "coordinates": [100, 0]
 *     };
 * var line = {
 *     "type": "LineString",
 *     "coordinates": [ [101, 0], [102, 1] ]
 *   };
 * var collection = turf.geometrycollection([[0,0],[10,10]]);
 *
 * //=collection
 */
module.exports.geometryCollection = function (geometries, properties) {
    return feature({
        type: 'GeometryCollection',
        geometries: geometries
    }, properties);
};

var factors = {
    miles: 3960,
    nauticalmiles: 3441.145,
    degrees: 57.2957795,
    radians: 1,
    inches: 250905600,
    yards: 6969600,
    meters: 6373000,
    metres: 6373000,
    kilometers: 6373,
    kilometres: 6373
};

/*
 * Convert a distance measurement from radians to a more friendly unit.
 *
 * @name radiansToDistance
 * @param {number} distance in radians across the sphere
 * @param {string=kilometers} units: one of miles, nauticalmiles, degrees, radians,
 * inches, yards, metres, meters, kilometres, kilometers.
 * @returns {number} distance
 */
module.exports.radiansToDistance = function (radians, units) {
    var factor = factors[units || 'kilometers'];
    if (factor === undefined) {
        throw new Error('Invalid unit');
    }
    return radians * factor;
};

/*
 * Convert a distance measurement from a real-world unit into radians
 *
 * @name distanceToRadians
 * @param {number} distance in real units
 * @param {string=kilometers} units: one of miles, nauticalmiles, degrees, radians,
 * inches, yards, metres, meters, kilometres, kilometers.
 * @returns {number} radians
 */
module.exports.distanceToRadians = function (distance, units) {
    var factor = factors[units || 'kilometers'];
    if (factor === undefined) {
        throw new Error('Invalid unit');
    }
    return distance / factor;
};

/*
 * Convert a distance measurement from a real-world unit into degrees
 *
 * @name distanceToRadians
 * @param {number} distance in real units
 * @param {string=kilometers} units: one of miles, nauticalmiles, degrees, radians,
 * inches, yards, metres, meters, kilometres, kilometers.
 * @returns {number} degrees
 */
module.exports.distanceToDegrees = function (distance, units) {
    var factor = factors[units || 'kilometers'];
    if (factor === undefined) {
        throw new Error('Invalid unit');
    }
    return (distance / factor) * 57.2958;
};

},{}],72:[function(require,module,exports){
var point = require('turf-helpers').point;
var polygon = require('turf-helpers').polygon;
var distance = require('turf-distance');
var featurecollection = require('turf-helpers').featureCollection;

//Precompute cosines and sines of angles used in hexagon creation
// for performance gain
var cosines = [];
var sines = [];
for (var i = 0; i < 6; i++) {
    var angle = 2 * Math.PI / 6 * i;
    cosines.push(Math.cos(angle));
    sines.push(Math.sin(angle));
}

/**
 * Takes a bounding box and a cell size in degrees and returns a {@link FeatureCollection} of flat-topped
 * hexagons ({@link Polygon} features) aligned in an "odd-q" vertical grid as
 * described in [Hexagonal Grids](http://www.redblobgames.com/grids/hexagons/).
 *
 * @name hexGrid
 * @param {Array<number>} bbox bounding box in [minX, minY, maxX, maxY] order
 * @param {number} cellSize dimension of cell in specified units
 * @param {string} units used in calculating cellWidth ('miles' or 'kilometers')
 * @param {boolean} triangles whether to return as triangles instead of hexagons
 * @return {FeatureCollection<Polygon>} a hexagonal grid
 * @example
 * var bbox = [-96,31,-84,40];
 * var cellWidth = 50;
 * var units = 'miles';
 *
 * var hexgrid = turf.hexGrid(bbox, cellWidth, units);
 *
 * //=hexgrid
 */
module.exports = function hexGrid(bbox, cellSize, units, triangles) {
    var xFraction = cellSize / (distance(point([bbox[0], bbox[1]]), point([bbox[2], bbox[1]]), units));
    var cellWidth = xFraction * (bbox[2] - bbox[0]);
    var yFraction = cellSize / (distance(point([bbox[0], bbox[1]]), point([bbox[0], bbox[3]]), units));
    var cellHeight = yFraction * (bbox[3] - bbox[1]);
    var radius = cellWidth / 2;

    var hex_width = radius * 2;
    var hex_height = Math.sqrt(3) / 2 * cellHeight;

    var box_width = bbox[2] - bbox[0];
    var box_height = bbox[3] - bbox[1];

    var x_interval = 3 / 4 * hex_width;
    var y_interval = hex_height;

    var x_span = box_width / (hex_width - radius / 2);
    var x_count = Math.ceil(x_span);
    if (Math.round(x_span) === x_count) {
        x_count++;
    }

    var x_adjust = ((x_count * x_interval - radius / 2) - box_width) / 2 - radius / 2;

    var y_count = Math.ceil(box_height / hex_height);

    var y_adjust = (box_height - y_count * hex_height) / 2;

    var hasOffsetY = y_count * hex_height - box_height > hex_height / 2;
    if (hasOffsetY) {
        y_adjust -= hex_height / 4;
    }

    var fc = featurecollection([]);
    for (var x = 0; x < x_count; x++) {
        for (var y = 0; y <= y_count; y++) {

            var isOdd = x % 2 === 1;
            if (y === 0 && isOdd) {
                continue;
            }

            if (y === 0 && hasOffsetY) {
                continue;
            }

            var center_x = x * x_interval + bbox[0] - x_adjust;
            var center_y = y * y_interval + bbox[1] + y_adjust;

            if (isOdd) {
                center_y -= hex_height / 2;
            }
            if (triangles) {
                fc.features.push.apply(fc.features, hexTriangles([center_x, center_y], cellWidth / 2, cellHeight / 2));
            } else {
                fc.features.push(hexagon([center_x, center_y], cellWidth / 2, cellHeight / 2));
            }
        }
    }

    return fc;
};

//Center should be [x, y]
function hexagon(center, rx, ry) {
    var vertices = [];
    for (var i = 0; i < 6; i++) {
        var x = center[0] + rx * cosines[i];
        var y = center[1] + ry * sines[i];
        vertices.push([x, y]);
    }
    //first and last vertex must be the same
    vertices.push(vertices[0]);
    return polygon([vertices]);
}

//Center should be [x, y]
function hexTriangles(center, rx, ry) {
    var triangles = [];
    for (var i = 0; i < 6; i++) {
        var vertices = [];
        vertices.push(center);
        vertices.push([
            center[0] + rx * cosines[i],
            center[1] + ry * sines[i]
        ]);
        vertices.push([
            center[0] + rx * cosines[(i + 1) % 6],
            center[1] + ry * sines[(i + 1) % 6]
        ]);
        vertices.push(center);
        triangles.push(polygon([vertices]));
    }
    return triangles;
}

},{"turf-distance":73,"turf-helpers":74}],73:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],74:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],75:[function(require,module,exports){
var invariant = require('turf-invariant');

// http://en.wikipedia.org/wiki/Even%E2%80%93odd_rule
// modified from: https://github.com/substack/point-in-polygon/blob/master/index.js
// which was modified from http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html

/**
 * Takes a {@link Point} and a {@link Polygon} or {@link MultiPolygon} and determines if the point resides inside the polygon. The polygon can
 * be convex or concave. The function accounts for holes.
 *
 * @name inside
 * @param {Feature<Point>} point input point
 * @param {Feature<(Polygon|MultiPolygon)>} polygon input polygon or multipolygon
 * @return {Boolean} `true` if the Point is inside the Polygon; `false` if the Point is not inside the Polygon
 * @example
 * var pt1 = {
 *   "type": "Feature",
 *   "properties": {
 *     "marker-color": "#f00"
 *   },
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-111.467285, 40.75766]
 *   }
 * };
 * var pt2 = {
 *   "type": "Feature",
 *   "properties": {
 *     "marker-color": "#0f0"
 *   },
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-111.873779, 40.647303]
 *   }
 * };
 * var poly = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [-112.074279, 40.52215],
 *       [-112.074279, 40.853293],
 *       [-111.610107, 40.853293],
 *       [-111.610107, 40.52215],
 *       [-112.074279, 40.52215]
 *     ]]
 *   }
 * };
 *
 * var features = {
 *   "type": "FeatureCollection",
 *   "features": [pt1, pt2, poly]
 * };
 *
 * //=features
 *
 * var isInside1 = turf.inside(pt1, poly);
 * //=isInside1
 *
 * var isInside2 = turf.inside(pt2, poly);
 * //=isInside2
 */
module.exports = function input(point, polygon) {
    var pt = invariant.getCoord(point);
    var polys = polygon.geometry.coordinates;
    // normalize to multipolygon
    if (polygon.geometry.type === 'Polygon') polys = [polys];

    for (var i = 0, insidePoly = false; i < polys.length && !insidePoly; i++) {
        // check if it is in the outer ring first
        if (inRing(pt, polys[i][0])) {
            var inHole = false;
            var k = 1;
            // check for the point in any of the holes
            while (k < polys[i].length && !inHole) {
                if (inRing(pt, polys[i][k])) {
                    inHole = true;
                }
                k++;
            }
            if (!inHole) insidePoly = true;
        }
    }
    return insidePoly;
};

// pt is [x,y] and ring is [[x,y], [x,y],..]
function inRing(pt, ring) {
    var isInside = false;
    for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        var xi = ring[i][0], yi = ring[i][1];
        var xj = ring[j][0], yj = ring[j][1];
        var intersect = ((yi > pt[1]) !== (yj > pt[1])) &&
        (pt[0] < (xj - xi) * (pt[1] - yi) / (yj - yi) + xi);
        if (intersect) isInside = !isInside;
    }
    return isInside;
}

},{"turf-invariant":76}],76:[function(require,module,exports){
arguments[4][14][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-invariant":79,"dup":14}],77:[function(require,module,exports){
// depend on jsts for now https://github.com/bjornharrtell/jsts/blob/master/examples/overlay.html
var jsts = require('jsts');

/**
 * Takes two {@link Polygon|polygons} and finds their intersection. If they share a border, returns the border; if they don't intersect, returns undefined.
 *
 * @name intersect
 * @param {Feature<Polygon>} poly1 the first polygon
 * @param {Feature<Polygon>} poly2 the second polygon
 * @return {(Feature<Polygon>|undefined|Feature<MultiLineString>)} if `poly1` and `poly2` overlap, returns a Polygon feature representing the area they overlap; if `poly1` and `poly2` do not overlap, returns `undefined`; if `poly1` and `poly2` share a border, a MultiLineString of the locations where their borders are shared
 * @example
 * var poly1 = {
 *   "type": "Feature",
 *   "properties": {
 *     "fill": "#0f0"
 *   },
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [-122.801742, 45.48565],
 *       [-122.801742, 45.60491],
 *       [-122.584762, 45.60491],
 *       [-122.584762, 45.48565],
 *       [-122.801742, 45.48565]
 *     ]]
 *   }
 * }
 * var poly2 = {
 *   "type": "Feature",
 *   "properties": {
 *     "fill": "#00f"
 *   },
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [-122.520217, 45.535693],
 *       [-122.64038, 45.553967],
 *       [-122.720031, 45.526554],
 *       [-122.669906, 45.507309],
 *       [-122.723464, 45.446643],
 *       [-122.532577, 45.408574],
 *       [-122.487258, 45.477466],
 *       [-122.520217, 45.535693]
 *     ]]
 *   }
 * }
 *
 * var polygons = {
 *   "type": "FeatureCollection",
 *   "features": [poly1, poly2]
 * };
 *
 * var intersection = turf.intersect(poly1, poly2);
 *
 * //=polygons
 *
 * //=intersection
 */
module.exports = function intersect(poly1, poly2) {
    var geom1, geom2;
    if (poly1.type === 'Feature') geom1 = poly1.geometry;
    else geom1 = poly1;
    if (poly2.type === 'Feature') geom2 = poly2.geometry;
    else geom2 = poly2;
    var reader = new jsts.io.GeoJSONReader();
    var a = reader.read(JSON.stringify(geom1));
    var b = reader.read(JSON.stringify(geom2));
    var intersection = a.intersection(b);

    if (intersection.isEmpty()) {
        return undefined;
    }

    var writer = new jsts.io.GeoJSONWriter();

    var geojsonGeometry = writer.write(intersection);
    return {
        type: 'Feature',
        properties: {},
        geometry: geojsonGeometry
    };
};

},{"jsts":78}],78:[function(require,module,exports){
arguments[4][20][0].apply(exports,arguments)
},{"dup":20}],79:[function(require,module,exports){
/**
 * Unwrap a coordinate from a Feature with a Point geometry, a Point
 * geometry, or a single coordinate.
 *
 * @param {*} obj any value
 * @returns {Array<number>} a coordinate
 */
function getCoord(obj) {
    if (Array.isArray(obj) &&
        typeof obj[0] === 'number' &&
        typeof obj[1] === 'number') {
        return obj;
    } else if (obj) {
        if (obj.type === 'Feature' &&
            obj.geometry &&
            obj.geometry.type === 'Point' &&
            Array.isArray(obj.geometry.coordinates)) {
            return obj.geometry.coordinates;
        } else if (obj.type === 'Point' &&
            Array.isArray(obj.coordinates)) {
            return obj.coordinates;
        }
    }
    throw new Error('A coordinate, feature, or point geometry is required');
}

/**
 * Enforce expectations about types of GeoJSON objects for Turf.
 *
 * @alias geojsonType
 * @param {GeoJSON} value any GeoJSON object
 * @param {string} type expected GeoJSON type
 * @param {string} name name of calling function
 * @throws {Error} if value is not the expected type.
 */
function geojsonType(value, type, name) {
    if (!type || !name) throw new Error('type and name required');

    if (!value || value.type !== type) {
        throw new Error('Invalid input to ' + name + ': must be a ' + type + ', given ' + value.type);
    }
}

/**
 * Enforce expectations about types of {@link Feature} inputs for Turf.
 * Internally this uses {@link geojsonType} to judge geometry types.
 *
 * @alias featureOf
 * @param {Feature} feature a feature with an expected geometry type
 * @param {string} type expected GeoJSON type
 * @param {string} name name of calling function
 * @throws {Error} error if value is not the expected type.
 */
function featureOf(feature, type, name) {
    if (!name) throw new Error('.featureOf() requires a name');
    if (!feature || feature.type !== 'Feature' || !feature.geometry) {
        throw new Error('Invalid input to ' + name + ', Feature with geometry required');
    }
    if (!feature.geometry || feature.geometry.type !== type) {
        throw new Error('Invalid input to ' + name + ': must be a ' + type + ', given ' + feature.geometry.type);
    }
}

/**
 * Enforce expectations about types of {@link FeatureCollection} inputs for Turf.
 * Internally this uses {@link geojsonType} to judge geometry types.
 *
 * @alias collectionOf
 * @param {FeatureCollection} featurecollection a featurecollection for which features will be judged
 * @param {string} type expected GeoJSON type
 * @param {string} name name of calling function
 * @throws {Error} if value is not the expected type.
 */
function collectionOf(featurecollection, type, name) {
    if (!name) throw new Error('.collectionOf() requires a name');
    if (!featurecollection || featurecollection.type !== 'FeatureCollection') {
        throw new Error('Invalid input to ' + name + ', FeatureCollection required');
    }
    for (var i = 0; i < featurecollection.features.length; i++) {
        var feature = featurecollection.features[i];
        if (!feature || feature.type !== 'Feature' || !feature.geometry) {
            throw new Error('Invalid input to ' + name + ', Feature with geometry required');
        }
        if (!feature.geometry || feature.geometry.type !== type) {
            throw new Error('Invalid input to ' + name + ': must be a ' + type + ', given ' + feature.geometry.type);
        }
    }
}

module.exports.geojsonType = geojsonType;
module.exports.collectionOf = collectionOf;
module.exports.featureOf = featureOf;
module.exports.getCoord = getCoord;

},{}],80:[function(require,module,exports){
/* eslint-disable */

/*
 * Copyright (c) 2010, Jason Davies.
 *
 * All rights reserved.  This code is based on Bradley White's Java version,
 * which is in turn based on Nicholas Yue's C++ version, which in turn is based
 * on Paul D. Bourke's original Fortran version.  See below for the respective
 * copyright notices.
 *
 * See http://local.wasp.uwa.edu.au/~pbourke/papers/conrec/ for the original
 * paper by Paul D. Bourke.
 *
 * The vector conversion code is based on http://apptree.net/conrec.htm by
 * Graham Cox.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 *     * Redistributions of source code must retain the above copyright
 *       notice, this list of conditions and the following disclaimer.
 *     * Redistributions in binary form must reproduce the above copyright
 *       notice, this list of conditions and the following disclaimer in the
 *       documentation and/or other materials provided with the distribution.
 *     * Neither the name of the <organization> nor the
 *       names of its contributors may be used to endorse or promote products
 *       derived from this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
 * ARE DISCLAIMED. IN NO EVENT SHALL <COPYRIGHT HOLDER> BE LIABLE FOR ANY
 * DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
 * THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */

/*
 * Copyright (c) 1996-1997 Nicholas Yue
 *
 * This software is copyrighted by Nicholas Yue. This code is based on Paul D.
 * Bourke's CONREC.F routine.
 *
 * The authors hereby grant permission to use, copy, and distribute this
 * software and its documentation for any purpose, provided that existing
 * copyright notices are retained in all copies and that this notice is
 * included verbatim in any distributions. Additionally, the authors grant
 * permission to modify this software and its documentation for any purpose,
 * provided that such modifications are not distributed without the explicit
 * consent of the authors and that existing copyright notices are retained in
 * all copies. Some of the algorithms implemented by this software are
 * patented, observe all applicable patent law.
 *
 * IN NO EVENT SHALL THE AUTHORS OR DISTRIBUTORS BE LIABLE TO ANY PARTY FOR
 * DIRECT, INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES ARISING OUT
 * OF THE USE OF THIS SOFTWARE, ITS DOCUMENTATION, OR ANY DERIVATIVES THEREOF,
 * EVEN IF THE AUTHORS HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 *
 * THE AUTHORS AND DISTRIBUTORS SPECIFICALLY DISCLAIM ANY WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.  THIS SOFTWARE IS
 * PROVIDED ON AN "AS IS" BASIS, AND THE AUTHORS AND DISTRIBUTORS HAVE NO
 * OBLIGATION TO PROVIDE MAINTENANCE, SUPPORT, UPDATES, ENHANCEMENTS, OR
 * MODIFICATIONS.
 */


  module.exports = Conrec;

  var EPSILON = 1e-10;

  function pointsEqual(a, b) {
      var x = a.x - b.x, y = a.y - b.y;
      return x * x + y * y < EPSILON;
  }

  function reverseList(list) {
      var pp = list.head;

      while (pp) {
      // swap prev/next pointers
          var temp = pp.next;
          pp.next = pp.prev;
          pp.prev = temp;

      // continue through the list
          pp = temp;
      }

    // swap head/tail pointers
      var temp = list.head;
      list.head = list.tail;
      list.tail = temp;
  }

  function ContourBuilder(level) {
      this.level = level;
      this.s = null;
      this.count = 0;
  }
  ContourBuilder.prototype.remove_seq = function (list) {
    // if list is the first item, static ptr s is updated
      if (list.prev) {
          list.prev.next = list.next;
      } else {
          this.s = list.next;
      }

      if (list.next) {
          list.next.prev = list.prev;
      }
      --this.count;
  };
  ContourBuilder.prototype.addSegment = function (a, b) {
      var ss = this.s;
      var ma = null;
      var mb = null;
      var prependA = false;
      var prependB = false;

      while (ss) {
          if (ma == null) {
        // no match for a yet
              if (pointsEqual(a, ss.head.p)) {
                  ma = ss;
                  prependA = true;
              } else if (pointsEqual(a, ss.tail.p)) {
                  ma = ss;
              }
          }
          if (mb == null) {
        // no match for b yet
              if (pointsEqual(b, ss.head.p)) {
                  mb = ss;
                  prependB = true;
              } else if (pointsEqual(b, ss.tail.p)) {
                  mb = ss;
              }
          }
      // if we matched both no need to continue searching
          if (mb != null && ma != null) {
              break;
          } else {
              ss = ss.next;
          }
      }

    // c is the case selector based on which of ma and/or mb are set
      var c = ((ma != null) ? 1 : 0) | ((mb != null) ? 2 : 0);

      switch (c) {
      case 0:   // both unmatched, add as new sequence
          var aa = {p: a, prev: null};
          var bb = {p: b, next: null};
          aa.next = bb;
          bb.prev = aa;

        // create sequence element and push onto head of main list. The order
        // of items in this list is unimportant
          ma = {head: aa, tail: bb, next: this.s, prev: null, closed: false};
          if (this.s) {
              this.s.prev = ma;
          }
          this.s = ma;

          ++this.count;    // not essential - tracks number of unmerged sequences
          break;

      case 1:   // a matched, b did not - thus b extends sequence ma
          var pp = {p: b};

          if (prependA) {
              pp.next = ma.head;
              pp.prev = null;
              ma.head.prev = pp;
              ma.head = pp;
          } else {
              pp.next = null;
              pp.prev = ma.tail;
              ma.tail.next = pp;
              ma.tail = pp;
          }
          break;

      case 2:   // b matched, a did not - thus a extends sequence mb
          var pp = {p: a};

          if (prependB) {
              pp.next = mb.head;
              pp.prev = null;
              mb.head.prev = pp;
              mb.head = pp;
          } else {
              pp.next = null;
              pp.prev = mb.tail;
              mb.tail.next = pp;
              mb.tail = pp;
          }
          break;

      case 3:   // both matched, can merge sequences
        // if the sequences are the same, do nothing, as we are simply closing this path (could set a flag)

          if (ma === mb) {
              var pp = {p: ma.tail.p, next: ma.head, prev: null};
              ma.head.prev = pp;
              ma.head = pp;
              ma.closed = true;
              break;
          }

        // there are 4 ways the sequence pair can be joined. The current setting of prependA and
        // prependB will tell us which type of join is needed. For head/head and tail/tail joins
        // one sequence needs to be reversed
          switch ((prependA ? 1 : 0) | (prependB ? 2 : 0)) {
          case 0:   // tail-tail
            // reverse ma and append to mb
              reverseList(ma);
            // fall through to head/tail case
          case 1:   // head-tail
            // ma is appended to mb and ma discarded
              mb.tail.next = ma.head;
              ma.head.prev = mb.tail;
              mb.tail = ma.tail;

            //discard ma sequence record
              this.remove_seq(ma);
              break;

          case 3:   // head-head
            // reverse ma and append mb to it
              reverseList(ma);
            // fall through to tail/head case
          case 2:   // tail-head
            // mb is appended to ma and mb is discarded
              ma.tail.next = mb.head;
              mb.head.prev = ma.tail;
              ma.tail = mb.tail;

            //discard mb sequence record
              this.remove_seq(mb);
              break;
          }
      }
  };

  /*
   * Implements CONREC.
   *
   * @private
   * @param {function} drawContour function for drawing contour.  Defaults to a
   *                               custom "contour builder", which populates the
   *                               contours property.
   */
  function Conrec(drawContour) {
      if (!drawContour) {
          var c = this;
          c.contours = {};
      /**
       * drawContour - interface for implementing the user supplied method to
       * render the countours.
       *
       * Draws a line between the start and end coordinates.
       *
       * @private
       * @param startX    - start coordinate for X
       * @param startY    - start coordinate for Y
       * @param endX      - end coordinate for X
       * @param endY      - end coordinate for Y
       * @param contourLevel - Contour level for line.
       */
          this.drawContour = function (startX, startY, endX, endY, contourLevel, k) {
              var cb = c.contours[k];
              if (!cb) {
                  cb = c.contours[k] = new ContourBuilder(contourLevel);
              }
              cb.addSegment({x: startX, y: startY}, {x: endX, y: endY});
          };
          this.contourList = function () {
              var l = [];
              var a = c.contours;
              for (var k in a) {
                  var s = a[k].s;
                  var level = a[k].level;
                  while (s) {
                      var h = s.head;
                      var l2 = [];
                      l2.level = level;
                      l2.k = k;
                      while (h && h.p) {
                          l2.push(h.p);
                          h = h.next;
                      }
                      l.push(l2);
                      s = s.next;
                  }
              }
              l.sort(function (a, b) { return a.k - b.k; });
              return l;
          };
      } else {
          this.drawContour = drawContour;
      }
      this.h  = new Array(5);
      this.sh = new Array(5);
      this.xh = new Array(5);
      this.yh = new Array(5);
  }

  /*
   * contour is a contouring subroutine for rectangularily spaced data
   *
   * It emits calls to a line drawing subroutine supplied by the user which
   * draws a contour map corresponding to real*4data on a randomly spaced
   * rectangular grid. The coordinates emitted are in the same units given in
   * the x() and y() arrays.
   *
   * Any number of contour levels may be specified but they must be in order of
   * increasing value.
   *
   *
   * @param {number[][]} d - matrix of data to contour
   * @param {number} ilb,iub,jlb,jub - index bounds of data matrix
   *
   *             The following two, one dimensional arrays (x and y) contain
   *             the horizontal and vertical coordinates of each sample points.
   * @param {number[]} x  - data matrix column coordinates
   * @param {number[]} y  - data matrix row coordinates
   * @param {number} nc   - number of contour levels
   * @param {number[]} z  - contour levels in increasing order.
   */
  Conrec.prototype.contour = function (d, ilb, iub, jlb, jub, x, y, nc, z) {
      var h = this.h, sh = this.sh, xh = this.xh, yh = this.yh;
      var drawContour = this.drawContour;
      this.contours = {};

      var xsect = function (p1, p2) {
          return (h[p2] * xh[p1] - h[p1] * xh[p2]) / (h[p2] - h[p1]);
      };

      var ysect = function (p1, p2) {
          return (h[p2] * yh[p1] - h[p1] * yh[p2]) / (h[p2] - h[p1]);
      };
      var m1;
      var m2;
      var m3;
      var case_value;
      var dmin;
      var dmax;
      var x1 = 0.0;
      var x2 = 0.0;
      var y1 = 0.0;
      var y2 = 0.0;

    // The indexing of im and jm should be noted as it has to start from zero
    // unlike the fortran counter part
      var im = [0, 1, 1, 0];
      var jm = [0, 0, 1, 1];

    // Note that castab is arranged differently from the FORTRAN code because
    // Fortran and C/C++ arrays are transposed of each other, in this case
    // it is more tricky as castab is in 3 dimensions
      var castab = [
          [
        [0, 0, 8], [0, 2, 5], [7, 6, 9]
          ],
          [
        [0, 3, 4], [1, 3, 1], [4, 3, 0]
          ],
          [
        [9, 6, 7], [5, 2, 0], [8, 0, 0]
          ]
      ];

      for (var j = (jub - 1); j >= jlb; j--) {
          for (var i = ilb; i <= iub - 1; i++) {
              var temp1, temp2;
              temp1 = Math.min(d[i][j], d[i][j + 1]);
              temp2 = Math.min(d[i + 1][j], d[i + 1][j + 1]);
              dmin  = Math.min(temp1, temp2);
              temp1 = Math.max(d[i][j], d[i][j + 1]);
              temp2 = Math.max(d[i + 1][j], d[i + 1][j + 1]);
              dmax  = Math.max(temp1, temp2);

              if (dmax >= z[0] && dmin <= z[nc - 1]) {
                  for (var k = 0; k < nc; k++) {
                      if (z[k] >= dmin && z[k] <= dmax) {
                          for (var m = 4; m >= 0; m--) {
                            if (m > 0) {
                  // The indexing of im and jm should be noted as it has to
                  // start from zero
                              h[m] = d[i + im[m - 1]][j + jm[m - 1]] - z[k];
                              xh[m] = x[i + im[m - 1]];
                              yh[m] = y[j + jm[m - 1]];
                          } else {
                              h[0] = 0.25 * (h[1] + h[2] + h[3] + h[4]);
                              xh[0] = 0.5 * (x[i] + x[i + 1]);
                              yh[0] = 0.5 * (y[j] + y[j + 1]);
                          }
                            if (h[m] > EPSILON) {
                              sh[m] = 1;
                          } else if (h[m] < -EPSILON) {
                            sh[m] = -1;
                        } else
                  sh[m] = 0;
                        }
              //
              // Note: at this stage the relative heights of the corners and the
              // centre are in the h array, and the corresponding coordinates are
              // in the xh and yh arrays. The centre of the box is indexed by 0
              // and the 4 corners by 1 to 4 as shown below.
              // Each triangle is then indexed by the parameter m, and the 3
              // vertices of each triangle are indexed by parameters m1,m2,and
              // m3.
              // It is assumed that the centre of the box is always vertex 2
              // though this isimportant only when all 3 vertices lie exactly on
              // the same contour level, in which case only the side of the box
              // is drawn.
              //
              //
              //      vertex 4 +-------------------+ vertex 3
              //               | \               / |
              //               |   \    m-3    /   |
              //               |     \       /     |
              //               |       \   /       |
              //               |  m=2    X   m=2   |       the centre is vertex 0
              //               |       /   \       |
              //               |     /       \     |
              //               |   /    m=1    \   |
              //               | /               \ |
              //      vertex 1 +-------------------+ vertex 2
              //
              //
              //
              //               Scan each triangle in the box
              //
                          for (m = 1; m <= 4; m++) {
                            m1 = m;
                            m2 = 0;
                            if (m != 4) {
                              m3 = m + 1;
                          } else {
                              m3 = 1;
                          }
                            case_value = castab[sh[m1] + 1][sh[m2] + 1][sh[m3] + 1];
                            if (case_value != 0) {
                              switch (case_value) {
                            case 1: // Line between vertices 1 and 2
                                x1 = xh[m1];
                                y1 = yh[m1];
                                x2 = xh[m2];
                                y2 = yh[m2];
                                break;
                            case 2: // Line between vertices 2 and 3
                                x1 = xh[m2];
                                y1 = yh[m2];
                                x2 = xh[m3];
                                y2 = yh[m3];
                                break;
                            case 3: // Line between vertices 3 and 1
                                x1 = xh[m3];
                                y1 = yh[m3];
                                x2 = xh[m1];
                                y2 = yh[m1];
                                break;
                            case 4: // Line between vertex 1 and side 2-3
                                x1 = xh[m1];
                                y1 = yh[m1];
                                x2 = xsect(m2, m3);
                                y2 = ysect(m2, m3);
                                break;
                            case 5: // Line between vertex 2 and side 3-1
                                x1 = xh[m2];
                                y1 = yh[m2];
                                x2 = xsect(m3, m1);
                                y2 = ysect(m3, m1);
                                break;
                            case 6: //  Line between vertex 3 and side 1-2
                                x1 = xh[m3];
                                y1 = yh[m3];
                                x2 = xsect(m1, m2);
                                y2 = ysect(m1, m2);
                                break;
                            case 7: // Line between sides 1-2 and 2-3
                                x1 = xsect(m1, m2);
                                y1 = ysect(m1, m2);
                                x2 = xsect(m2, m3);
                                y2 = ysect(m2, m3);
                                break;
                            case 8: // Line between sides 2-3 and 3-1
                                x1 = xsect(m2, m3);
                                y1 = ysect(m2, m3);
                                x2 = xsect(m3, m1);
                                y2 = ysect(m3, m1);
                                break;
                            case 9: // Line between sides 3-1 and 1-2
                                x1 = xsect(m3, m1);
                                y1 = ysect(m3, m1);
                                x2 = xsect(m1, m2);
                                y2 = ysect(m1, m2);
                                break;
                            default:
                                break;
                            }
                  // Put your processing code here and comment out the printf
                  //printf("%f %f %f %f %f\n",x1,y1,x2,y2,z[k]);
                              drawContour(x1, y1, x2, y2, z[k], k);
                          }
                        }
                      }
                  }
              }
          }
      }
  };

},{}],81:[function(require,module,exports){
//https://github.com/jasondavies/conrec.js
//http://stackoverflow.com/questions/263305/drawing-a-topographical-map
var tin = require('turf-tin');
var inside = require('turf-inside');
var grid = require('turf-grid');
var bbox = require('turf-bbox');
var planepoint = require('turf-planepoint');
var featurecollection = require('turf-helpers').featureCollection;
var linestring = require('turf-helpers').lineString;
var square = require('turf-square');
var Conrec = require('./conrec');

/**
 * Takes {@link Point|points} with z-values and an array of
 * value breaks and generates [isolines](http://en.wikipedia.org/wiki/Isoline).
 *
 * @name isolines
 * @param {FeatureCollection<Point>} points input points
 * @param {string} z the property name in `points` from which z-values will be pulled
 * @param {number} resolution resolution of the underlying grid
 * @param {Array<number>} breaks where to draw contours
 * @returns {FeatureCollection<LineString>} isolines
 * @example
 * // create random points with random
 * // z-values in their properties
 * var points = turf.random('point', 100, {
 *   bbox: [0, 30, 20, 50]
 * });
 * for (var i = 0; i < points.features.length; i++) {
 *   points.features[i].properties.z = Math.random() * 10;
 * }
 * var breaks = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
 * var isolined = turf.isolines(points, 'z', 15, breaks);
 * //=isolined
 */
module.exports = function (points, z, resolution, breaks) {
    var tinResult = tin(points, z);
    var bboxBBox = bbox(points);
    var squareBBox = square(bboxBBox);
    var gridResult = grid(squareBBox, resolution);
    var data = [];

    for (var i = 0; i < gridResult.features.length; i++) {
        var pt = gridResult.features[i];
        for (var j = 0; j < tinResult.features.length; j++) {
            var triangle = tinResult.features[j];
            if (inside(pt, triangle)) {
                pt.properties = {};
                pt.properties[z] = planepoint(pt, triangle);
            }
        }
    }

    var depth = Math.sqrt(gridResult.features.length);
    for (var x = 0; x < depth; x++) {
        var xGroup = gridResult.features.slice(x * depth, (x + 1) * depth);
        var xFlat = [];

        for (var g = 0; g < xGroup.length; g++) {
            if (xGroup[g].properties) {
                xFlat.push(xGroup[g].properties[z]);
            } else {
                xFlat.push(0);
            }
        }
        data.push(xFlat);
    }
    var interval = (squareBBox[2] - squareBBox[0]) / depth;
    var xCoordinates = [];
    var yCoordinates = [];
    for (var d = 0; d < depth; d++) {
        xCoordinates.push(d * interval + squareBBox[0]);
        yCoordinates.push(d * interval + squareBBox[1]);
    }

    var c = new Conrec();
    c.contour(data, 0, resolution, 0, resolution, xCoordinates, yCoordinates, breaks.length, breaks);
    var contourList = c.contourList();

    var fc = featurecollection([]);
    contourList.forEach(function (c) {
        if (c.length > 2) {
            var polyCoordinates = [];
            c.forEach(function (coord) {
                polyCoordinates.push([coord.x, coord.y]);
            });
            var poly = linestring(polyCoordinates);
            poly.properties = {};
            poly.properties[z] = c.level;

            fc.features.push(poly);
        }
    });

    return fc;
};

},{"./conrec":80,"turf-bbox":82,"turf-grid":83,"turf-helpers":84,"turf-inside":85,"turf-planepoint":86,"turf-square":88,"turf-tin":89}],82:[function(require,module,exports){
arguments[4][23][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-bbox":11,"dup":23}],83:[function(require,module,exports){
var point = require('turf-point');

/**
 * Takes a bounding box and a cell depth and returns a {@link FeatureCollection} of {@link Point} features in a grid.
 *
 * @module turf/grid
 * @category interpolation
 * @param {Array<number>} extent extent in [minX, minY, maxX, maxY] order
 * @param {Number} depth how many cells to output
 * @return {FeatureCollection} grid as FeatureCollection with {@link Point} features
 * @example
 * var extent = [-70.823364, -33.553984, -70.473175, -33.302986];
 * var depth = 10;
 *
 * var grid = turf.grid(extent, depth);
 *
 * //=grid
 */
module.exports = function(extents, depth) {
  var xmin = extents[0];
  var ymin = extents[1];
  var xmax = extents[2];
  var ymax = extents[3];
  var interval = (xmax - xmin) / depth;
  var coords = [];
  var fc = {
    type: 'FeatureCollection',
    features: []
  };

  for (var x=0; x<=depth; x++){
    for (var y=0;y<=depth; y++){
      fc.features.push(point([(x * interval) + xmin, (y * interval) + ymin]));
    }
  }
  return fc;
}

},{"turf-point":87}],84:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],85:[function(require,module,exports){
arguments[4][29][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-inside":75,"dup":29}],86:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-planepoint");

},{"/Users/tmcw/src/turf/packages/turf-planepoint":105}],87:[function(require,module,exports){
/**
 * Takes coordinates and properties (optional) and returns a new {@link Point} feature.
 *
 * @module turf/point
 * @category helper
 * @param {number} longitude position west to east in decimal degrees
 * @param {number} latitude position south to north in decimal degrees
 * @param {Object} properties an Object that is used as the {@link Feature}'s
 * properties
 * @return {Point} a Point feature
 * @example
 * var pt1 = turf.point([-75.343, 39.984]);
 *
 * //=pt1
 */
var isArray = Array.isArray || function(arg) {
  return Object.prototype.toString.call(arg) === '[object Array]';
};
module.exports = function(coordinates, properties) {
  if (!isArray(coordinates)) throw new Error('Coordinates must be an array');
  if (coordinates.length < 2) throw new Error('Coordinates must be at least 2 numbers long');
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: coordinates
    },
    properties: properties || {}
  };
};

},{}],88:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-square");

},{"/Users/tmcw/src/turf/packages/turf-square":129}],89:[function(require,module,exports){
arguments[4][34][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-tin":136,"dup":34}],90:[function(require,module,exports){
/**
 * Takes a {@link Polygon|polygon} and returns {@link Point|points} at all self-intersections.
 *
 * @name kinks
 * @param {Feature<Polygon>|Polygon} polygon input polygon
 * @returns {FeatureCollection<Point>} self-intersections
 * @example
 * var poly = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [-12.034835, 8.901183],
 *       [-12.060413, 8.899826],
 *       [-12.03638, 8.873199],
 *       [-12.059383, 8.871418],
 *       [-12.034835, 8.901183]
 *     ]]
 *   }
 * };
 *
 * var kinks = turf.kinks(poly);
 *
 * var resultFeatures = kinks.intersections.features.concat(poly);
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": resultFeatures
 * };
 *
 * //=result
 */

var point = require('turf-helpers').point;

module.exports = function (polyIn) {
    var poly;
    var results = {
        type: 'FeatureCollection',
        features: []
    };
    if (polyIn.type === 'Feature') {
        poly = polyIn.geometry;
    } else {
        poly = polyIn;
    }
    poly.coordinates.forEach(function (ring1) {
        poly.coordinates.forEach(function (ring2) {
            for (var i = 0; i < ring1.length - 1; i++) {
                for (var k = 0; k < ring2.length - 1; k++) {
                    // don't check adjacent sides of a given ring, since of course they intersect in a vertex.
                    if (ring1 === ring2 && (Math.abs(i - k) === 1 || Math.abs(i - k) === ring1.length - 2)) {
                        continue;
                    }

                    var intersection = lineIntersects(ring1[i][0], ring1[i][1], ring1[i + 1][0], ring1[i + 1][1],
                        ring2[k][0], ring2[k][1], ring2[k + 1][0], ring2[k + 1][1]);
                    if (intersection) {
                        results.features.push(point([intersection[0], intersection[1]]));
                    }
                }
            }
        });
    });
    return results;
};


// modified from http://jsfiddle.net/justin_c_rounds/Gd2S2/light/
function lineIntersects(line1StartX, line1StartY, line1EndX, line1EndY, line2StartX, line2StartY, line2EndX, line2EndY) {
    // if the lines intersect, the result contains the x and y of the intersection (treating the lines as infinite) and booleans for whether line segment 1 or line segment 2 contain the point
    var denominator, a, b, numerator1, numerator2,
        result = {
            x: null,
            y: null,
            onLine1: false,
            onLine2: false
        };
    denominator = ((line2EndY - line2StartY) * (line1EndX - line1StartX)) - ((line2EndX - line2StartX) * (line1EndY - line1StartY));
    if (denominator === 0) {
        if (result.x !== null && result.y !== null) {
            return result;
        } else {
            return false;
        }
    }
    a = line1StartY - line2StartY;
    b = line1StartX - line2StartX;
    numerator1 = ((line2EndX - line2StartX) * a) - ((line2EndY - line2StartY) * b);
    numerator2 = ((line1EndX - line1StartX) * a) - ((line1EndY - line1StartY) * b);
    a = numerator1 / denominator;
    b = numerator2 / denominator;

    // if we cast these lines infinitely in both directions, they intersect here:
    result.x = line1StartX + (a * (line1EndX - line1StartX));
    result.y = line1StartY + (a * (line1EndY - line1StartY));

    // if line1 is a segment and line2 is infinite, they intersect if:
    if (a >= 0 && a <= 1) {
        result.onLine1 = true;
    }
    // if line2 is a segment and line1 is infinite, they intersect if:
    if (b >= 0 && b <= 1) {
        result.onLine2 = true;
    }
    // if line1 and line2 are segments, they intersect if both of the above are true
    if (result.onLine1 && result.onLine2) {
        return [result.x, result.y];
    } else {
        return false;
    }
}

},{"turf-helpers":91}],91:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],92:[function(require,module,exports){
var distance = require('turf-distance');
var point = require('turf-helpers').point;

/**
 * Takes a {@link LineString|line} and measures its length in the specified units.
 *
 * @name lineDistance
 * @param {Feature<LineString>} line line to measure
 * @param {String} [units=kilometers] can be degrees, radians, miles, or kilometers
 * @return {Number} length of the input line
 * @example
 * var line = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "LineString",
 *     "coordinates": [
 *       [-77.031669, 38.878605],
 *       [-77.029609, 38.881946],
 *       [-77.020339, 38.884084],
 *       [-77.025661, 38.885821],
 *       [-77.021884, 38.889563],
 *       [-77.019824, 38.892368]
 *     ]
 *   }
 * };
 *
 * var length = turf.lineDistance(line, 'miles');
 *
 * //=line
 *
 * //=length
 */
module.exports = function lineDistance(line, units) {
    if (line.type === 'FeatureCollection') {
        return line.features.reduce(function (memo, feature) {
            return memo + lineDistance(feature, units);
        }, 0);
    }

    var geometry = line.type === 'Feature' ? line.geometry : line;
    var d, i;

    if (geometry.type === 'LineString') {
        return length(geometry.coordinates, units);
    } else if (geometry.type === 'Polygon' || geometry.type === 'MultiLineString') {
        d = 0;
        for (i = 0; i < geometry.coordinates.length; i++) {
            d += length(geometry.coordinates[i], units);
        }
        return d;
    } else if (line.type === 'MultiPolygon') {
        d = 0;
        for (i = 0; i < geometry.coordinates.length; i++) {
            for (var j = 0; j < geometry.coordinates[i].length; j++) {
                d += length(geometry.coordinates[i][j], units);
            }
        }
        return d;
    } else {
        throw new Error('input must be a LineString, MultiLineString, ' +
            'Polygon, or MultiPolygon Feature or Geometry (or a FeatureCollection ' +
            'containing only those types)');
    }

};

function length(coords, units) {
    var travelled = 0;
    var prevCoords = point(coords[0]);
    var curCoords = point(coords[0]);
    var temp;
    for (var i = 1; i < coords.length; i++) {
        curCoords.geometry.coordinates = coords[i];
        travelled += distance(prevCoords, curCoords, units);
        temp = prevCoords;
        prevCoords = curCoords;
        curCoords = temp;
    }
    return travelled;
}

},{"turf-distance":93,"turf-helpers":94}],93:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],94:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],95:[function(require,module,exports){
var linestring = require('turf-helpers').lineString;
var pointOnLine = require('turf-point-on-line');

/**
 * Takes a {@link LineString|line}, a start {@link Point}, and a stop point
 * and returns a subsection of the line in-between those points.
 * The start & stop points don't need to fall exactly on the line.
 *
 * This can be useful for extracting only the part of a route between waypoints.
 *
 * @name lineSlice
 * @param {Feature<Point>} point1 starting point
 * @param {Feature<Point>} point2 stopping point
 * @param {Feature<LineString>|LineString} line line to slice
 * @return {Feature<LineString>} sliced line
 * @example
 * var line = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "LineString",
 *     "coordinates": [
 *       [-77.031669, 38.878605],
 *       [-77.029609, 38.881946],
 *       [-77.020339, 38.884084],
 *       [-77.025661, 38.885821],
 *       [-77.021884, 38.889563],
 *       [-77.019824, 38.892368]
 *     ]
 *   }
 * };
 * var start = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-77.029609, 38.881946]
 *   }
 * };
 * var stop = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-77.021884, 38.889563]
 *   }
 * };
 *
 * var sliced = turf.lineSlice(start, stop, line);
 *
 * //=line
 *
 * //=sliced
 */

module.exports = function lineSlice(startPt, stopPt, line) {
    var coords;
    if (line.type === 'Feature') {
        coords = line.geometry.coordinates;
    } else if (line.type === 'LineString') {
        coords = line.coordinates;
    } else {
        throw new Error('input must be a LineString Feature or Geometry');
    }

    var startVertex = pointOnLine(line, startPt);
    var stopVertex = pointOnLine(line, stopPt);
    var ends;
    if (startVertex.properties.index <= stopVertex.properties.index) {
        ends = [startVertex, stopVertex];
    } else {
        ends = [stopVertex, startVertex];
    }
    var clipLine = linestring([ends[0].geometry.coordinates], {});
    for (var i = ends[0].properties.index + 1; i < ends[1].properties.index + 1; i++) {
        clipLine.geometry.coordinates.push(coords[i]);
    }
    clipLine.geometry.coordinates.push(ends[1].geometry.coordinates);
    return clipLine;
};

},{"turf-helpers":96,"turf-point-on-line":97}],96:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],97:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-point-on-line");

},{"/Users/tmcw/src/turf/packages/turf-point-on-line":109}],98:[function(require,module,exports){
/**
 * Iterate over coordinates in any GeoJSON object, similar to
 * Array.forEach.
 *
 * @param {Object} layer any GeoJSON object
 * @param {Function} callback a method that takes (value)
 * @param {boolean=} excludeWrapCoord whether or not to include
 * the final coordinate of LinearRings that wraps the ring in its iteration.
 * @example
 * var point = { type: 'Point', coordinates: [0, 0] };
 * coordEach(point, function(coords) {
 *   // coords is equal to [0, 0]
 * });
 */
function coordEach(layer, callback, excludeWrapCoord) {
    var i, j, k, g, l, geometry, stopG, coords,
        geometryMaybeCollection,
        wrapShrink = 0,
        isGeometryCollection,
        isFeatureCollection = layer.type === 'FeatureCollection',
        isFeature = layer.type === 'Feature',
        stop = isFeatureCollection ? layer.features.length : 1;

  // This logic may look a little weird. The reason why it is that way
  // is because it's trying to be fast. GeoJSON supports multiple kinds
  // of objects at its root: FeatureCollection, Features, Geometries.
  // This function has the responsibility of handling all of them, and that
  // means that some of the `for` loops you see below actually just don't apply
  // to certain inputs. For instance, if you give this just a
  // Point geometry, then both loops are short-circuited and all we do
  // is gradually rename the input until it's called 'geometry'.
  //
  // This also aims to allocate as few resources as possible: just a
  // few numbers and booleans, rather than any temporary arrays as would
  // be required with the normalization approach.
    for (i = 0; i < stop; i++) {

        geometryMaybeCollection = (isFeatureCollection ? layer.features[i].geometry :
        (isFeature ? layer.geometry : layer));
        isGeometryCollection = geometryMaybeCollection.type === 'GeometryCollection';
        stopG = isGeometryCollection ? geometryMaybeCollection.geometries.length : 1;

        for (g = 0; g < stopG; g++) {
            geometry = isGeometryCollection ?
            geometryMaybeCollection.geometries[g] : geometryMaybeCollection;
            coords = geometry.coordinates;

            wrapShrink = (excludeWrapCoord &&
                (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon')) ?
                1 : 0;

            if (geometry.type === 'Point') {
                callback(coords);
            } else if (geometry.type === 'LineString' || geometry.type === 'MultiPoint') {
                for (j = 0; j < coords.length; j++) callback(coords[j]);
            } else if (geometry.type === 'Polygon' || geometry.type === 'MultiLineString') {
                for (j = 0; j < coords.length; j++)
                    for (k = 0; k < coords[j].length - wrapShrink; k++)
                        callback(coords[j][k]);
            } else if (geometry.type === 'MultiPolygon') {
                for (j = 0; j < coords.length; j++)
                    for (k = 0; k < coords[j].length; k++)
                        for (l = 0; l < coords[j][k].length - wrapShrink; l++)
                            callback(coords[j][k][l]);
            } else {
                throw new Error('Unknown Geometry Type');
            }
        }
    }
}
module.exports.coordEach = coordEach;

/**
 * Reduce coordinates in any GeoJSON object into a single value,
 * similar to how Array.reduce works. However, in this case we lazily run
 * the reduction, so an array of all coordinates is unnecessary.
 *
 * @param {Object} layer any GeoJSON object
 * @param {Function} callback a method that takes (memo, value) and returns
 * a new memo
 * @param {*} memo the starting value of memo: can be any type.
 * @param {boolean=} excludeWrapCoord whether or not to include
 * the final coordinate of LinearRings that wraps the ring in its iteration.
 * @return {*} combined value
 */
function coordReduce(layer, callback, memo, excludeWrapCoord) {
    coordEach(layer, function (coord) {
        memo = callback(memo, coord);
    }, excludeWrapCoord);
    return memo;
}
module.exports.coordReduce = coordReduce;

/**
 * Iterate over property objects in any GeoJSON object, similar to
 * Array.forEach.
 *
 * @param {Object} layer any GeoJSON object
 * @param {Function} callback a method that takes (value)
 * @example
 * var point = { type: 'Feature', geometry: null, properties: { foo: 1 } };
 * propEach(point, function(props) {
 *   // props is equal to { foo: 1}
 * });
 */
function propEach(layer, callback) {
    var i;
    switch (layer.type) {
    case 'FeatureCollection':
        for (i = 0; i < layer.features.length; i++) {
            callback(layer.features[i].properties);
        }
        break;
    case 'Feature':
        callback(layer.properties);
        break;
    }
}
module.exports.propEach = propEach;

/**
 * Reduce properties in any GeoJSON object into a single value,
 * similar to how Array.reduce works. However, in this case we lazily run
 * the reduction, so an array of all properties is unnecessary.
 *
 * @param {Object} layer any GeoJSON object
 * @param {Function} callback a method that takes (memo, coord) and returns
 * a new memo
 * @param {*} memo the starting value of memo: can be any type.
 * @return {*} combined value
 */
function propReduce(layer, callback, memo) {
    propEach(layer, function (prop) {
        memo = callback(memo, prop);
    });
    return memo;
}
module.exports.propReduce = propReduce;

/**
 * Iterate over features in any GeoJSON object, similar to
 * Array.forEach.
 *
 * @param {Object} layer any GeoJSON object
 * @param {Function} callback a method that takes (value)
 * @example
 * var feature = { type: 'Feature', geometry: null, properties: {} };
 * featureEach(feature, function(feature) {
 *   // feature == feature
 * });
 */
function featureEach(layer, callback) {
    if (layer.type === 'Feature') {
        callback(layer);
    } else if (layer.type === 'FeatureCollection') {
        for (var i = 0; i < layer.features.length; i++) {
            callback(layer.features[i]);
        }
    }
}
module.exports.featureEach = featureEach;

/**
 * Get all coordinates from any GeoJSON object, returning an array of coordinate
 * arrays.
 * @param {Object} layer any GeoJSON object
 * @return {Array<Array<Number>>} coordinate position array
 */
function coordAll(layer) {
    var coords = [];
    coordEach(layer, function (coord) {
        coords.push(coord);
    });
    return coords;
}
module.exports.coordAll = coordAll;

},{}],99:[function(require,module,exports){
var bearing = require('turf-bearing');
var destination = require('turf-destination');
var distance = require('turf-distance');

/**
 * Takes two {@link Point|points} and returns a point midway between them.
 * The midpoint is calculated geodesically, meaning the curvature of the earth is taken into account.
 *
 * @name midpoint
 * @param {Feature<Point>} from first point
 * @param {Feature<Point>} to second point
 * @return {Feature<Point>} a point midway between `pt1` and `pt2`
 * @example
 * var pt1 = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [144.834823, -37.771257]
 *   }
 * };
 * var pt2 = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [145.14244, -37.830937]
 *   }
 * };
 *
 * var midpointed = turf.midpoint(pt1, pt2);
 * midpointed.properties['marker-color'] = '#f00';
 *
 *
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": [pt1, pt2, midpointed]
 * };
 *
 * //=result
 */
module.exports = function (from, to) {
    var dist = distance(from, to, 'miles');
    var heading = bearing(from, to);
    var midpoint = destination(from, dist / 2, heading, 'miles');

    return midpoint;
};

},{"turf-bearing":100,"turf-destination":101,"turf-distance":102}],100:[function(require,module,exports){
arguments[4][2][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-bearing":13,"dup":2}],101:[function(require,module,exports){
arguments[4][3][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-destination":55,"dup":3}],102:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],103:[function(require,module,exports){
var distance = require('turf-distance');

/**
 * Takes a reference {@link Point|point} and a FeatureCollection of Features
 * with Point geometries and returns the
 * point from the FeatureCollection closest to the reference. This calculation
 * is geodesic.
 *
 * @name nearest
 * @param {Feature<Point>} targetPoint the reference point
 * @param {FeatureCollection<Point>} points against input point set
 * @return {Feature<Point>} the closest point in the set to the reference point
 * @example
 * var point = {
 *   "type": "Feature",
 *   "properties": {
 *     "marker-color": "#0f0"
 *   },
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [28.965797, 41.010086]
 *   }
 * };
 * var against = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [28.973865, 41.011122]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [28.948459, 41.024204]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [28.938674, 41.013324]
 *       }
 *     }
 *   ]
 * };
 *
 * var nearest = turf.nearest(point, against);
 * nearest.properties['marker-color'] = '#f00';
 *
 * var resultFeatures = against.features.concat(point);
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": resultFeatures
 * };
 *
 * //=result
 */
module.exports = function (targetPoint, points) {
    var nearestPoint, minDist = Infinity;
    for (var i = 0; i < points.features.length; i++) {
        var distanceToPoint = distance(targetPoint, points.features[i], 'miles');
        if (distanceToPoint < minDist) {
            nearestPoint = points.features[i];
            minDist = distanceToPoint;
        }
    }
    return nearestPoint;
};

},{"turf-distance":104}],104:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],105:[function(require,module,exports){
/**
 * Takes a triangular plane as a {@link Polygon}
 * and a {@link Point} within that triangle and returns the z-value
 * at that point. The Polygon needs to have properties `a`, `b`, and `c`
 * that define the values at its three corners.
 *
 * @name planepoint
 * @param {Feature<Point>} point the Point for which a z-value will be calculated
 * @param {Feature<Polygon>} triangle a Polygon feature with three vertices
 * @return {Number} the z-value for `interpolatedPoint`
 * @example
 * var point = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-75.3221, 39.529]
 *   }
 * };
 * var point = turf.point([-75.3221, 39.529]);
 * // triangle is a polygon with "a", "b",
 * // and "c" values representing
 * // the values of the coordinates in order.
 * var triangle = {
 *   "type": "Feature",
 *   "properties": {
 *     "a": 11,
 *     "b": 122,
 *     "c": 44
 *   },
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [-75.1221, 39.57],
 *       [-75.58, 39.18],
 *       [-75.97, 39.86],
 *       [-75.1221, 39.57]
 *     ]]
 *   }
 * };
 *
 * var features = {
 *   "type": "FeatureCollection",
 *   "features": [triangle, point]
 * };
 *
 * var zValue = turf.planepoint(point, triangle);
 *
 * //=features
 *
 * //=zValue
 */
module.exports = function (point, triangle) {
    var x = point.geometry.coordinates[0],
        y = point.geometry.coordinates[1],
        x1 = triangle.geometry.coordinates[0][0][0],
        y1 = triangle.geometry.coordinates[0][0][1],
        z1 = triangle.properties.a,
        x2 = triangle.geometry.coordinates[0][1][0],
        y2 = triangle.geometry.coordinates[0][1][1],
        z2 = triangle.properties.b,
        x3 = triangle.geometry.coordinates[0][2][0],
        y3 = triangle.geometry.coordinates[0][2][1],
        z3 = triangle.properties.c;

    var z = (z3 * (x - x1) * (y - y2) + z1 * (x - x2) * (y - y3) + z2 * (x - x3) * (y - y1) -
      z2 * (x - x1) * (y - y3) - z3 * (x - x2) * (y - y1) - z1 * (x - x3) * (y - y2)) /
      ((x - x1) * (y - y2) + (x - x2) * (y - y3) + (x - x3) * (y - y1) -
       (x - x1) * (y - y3) - (x - x2) * (y - y1) - (x - x3) * (y - y2));

    return z;
};

},{}],106:[function(require,module,exports){
var point = require('turf-helpers').point;
var featurecollection = require('turf-helpers').featureCollection;
var distance = require('turf-distance');
/**
 * Takes a bounding box and a cell depth and returns a set of {@link Point|points} in a grid.
 *
 * @name pointGrid
 * @param {Array<number>} bbox extent in [minX, minY, maxX, maxY] order
 * @param {number} cellSize the distance across each cell
 * @param {string} [units=kilometers] used in calculating cellWidth, can be degrees, radians, miles, or kilometers
 * @return {FeatureCollection<Point>} grid of points
 * @example
 * var extent = [-70.823364, -33.553984, -70.473175, -33.302986];
 * var cellWidth = 3;
 * var units = 'miles';
 *
 * var grid = turf.pointGrid(extent, cellWidth, units);
 *
 * //=grid
 */
module.exports = function pointGrid(bbox, cellSize, units) {
    var fc = featurecollection([]);
    var xFraction = cellSize / (distance(point([bbox[0], bbox[1]]), point([bbox[2], bbox[1]]), units));
    var cellWidth = xFraction * (bbox[2] - bbox[0]);
    var yFraction = cellSize / (distance(point([bbox[0], bbox[1]]), point([bbox[0], bbox[3]]), units));
    var cellHeight = yFraction * (bbox[3] - bbox[1]);

    var currentX = bbox[0];
    while (currentX <= bbox[2]) {
        var currentY = bbox[1];
        while (currentY <= bbox[3]) {
            fc.features.push(point([currentX, currentY]));

            currentY += cellHeight;
        }
        currentX += cellWidth;
    }

    return fc;
};

},{"turf-distance":107,"turf-helpers":108}],107:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],108:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],109:[function(require,module,exports){
var distance = require('turf-distance');
var point = require('turf-helpers').point;
var bearing = require('turf-bearing');
var destination = require('turf-destination');

/**
 * Takes a {@link Point} and a {@link LineString} and calculates the closest Point on the LineString.
 *
 * @name pointOnLine
 * @param {Feature<LineString>} line line to snap to
 * @param {Feature<Point>} point point to snap from
 * @return {Feature<Point>} closest point on the `line` to `point`
 * @example
 * var line = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "LineString",
 *     "coordinates": [
 *       [-77.031669, 38.878605],
 *       [-77.029609, 38.881946],
 *       [-77.020339, 38.884084],
 *       [-77.025661, 38.885821],
 *       [-77.021884, 38.889563],
 *       [-77.019824, 38.892368]
 *     ]
 *   }
 * };
 * var pt = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Point",
 *     "coordinates": [-77.037076, 38.884017]
 *   }
 * };
 *
 * var snapped = turf.pointOnLine(line, pt);
 * snapped.properties['marker-color'] = '#00f'
 *
 * var result = {
 *   "type": "FeatureCollection",
 *   "features": [line, pt, snapped]
 * };
 *
 * //=result
 */

module.exports = function (line, pt) {
    var coords;
    if (line.type === 'Feature') {
        coords = line.geometry.coordinates;
    } else if (line.type === 'LineString') {
        coords = line.coordinates;
    } else {
        throw new Error('input must be a LineString Feature or Geometry');
    }

    return pointOnLine(pt, coords);
};

function pointOnLine(pt, coords) {
    var units = 'miles';
    var closestPt = point([Infinity, Infinity], {
        dist: Infinity
    });
    for (var i = 0; i < coords.length - 1; i++) {
        var start = point(coords[i]);
        var stop = point(coords[i + 1]);
        //start
        start.properties.dist = distance(pt, start, units);
        //stop
        stop.properties.dist = distance(pt, stop, units);
        //perpendicular
        var heightDistance = Math.max(start.properties.dist, stop.properties.dist);
        var direction = bearing(start, stop);
        var perpendicularPt1 = destination(pt, heightDistance, direction + 90, units);
        var perpendicularPt2 = destination(pt, heightDistance, direction - 90, units);
        var intersect = lineIntersects(
        perpendicularPt1.geometry.coordinates[0],
        perpendicularPt1.geometry.coordinates[1],
        perpendicularPt2.geometry.coordinates[0],
        perpendicularPt2.geometry.coordinates[1],
        start.geometry.coordinates[0],
        start.geometry.coordinates[1],
        stop.geometry.coordinates[0],
        stop.geometry.coordinates[1]
        );
        var intersectPt;
        if (intersect) {
            intersectPt = point(intersect);
            intersectPt.properties.dist = distance(pt, intersectPt, units);
        }

        if (start.properties.dist < closestPt.properties.dist) {
            closestPt = start;
            closestPt.properties.index = i;
        }
        if (stop.properties.dist < closestPt.properties.dist) {
            closestPt = stop;
            closestPt.properties.index = i;
        }
        if (intersectPt && intersectPt.properties.dist < closestPt.properties.dist) {
            closestPt = intersectPt;
            closestPt.properties.index = i;
        }
    }

    return closestPt;
}

// modified from http://jsfiddle.net/justin_c_rounds/Gd2S2/light/
function lineIntersects(line1StartX, line1StartY, line1EndX, line1EndY, line2StartX, line2StartY, line2EndX, line2EndY) {
    // if the lines intersect, the result contains the x and y of the intersection (treating the lines as infinite) and booleans for whether line segment 1 or line segment 2 contain the point
    var denominator, a, b, numerator1, numerator2;
    var result = {
        x: null,
        y: null,
        onLine1: false,
        onLine2: false
    };
    denominator = ((line2EndY - line2StartY) * (line1EndX - line1StartX)) - ((line2EndX - line2StartX) * (line1EndY - line1StartY));
    if (denominator === 0) {
        if (result.x !== null && result.y !== null) {
            return result;
        } else {
            return false;
        }
    }
    a = line1StartY - line2StartY;
    b = line1StartX - line2StartX;
    numerator1 = ((line2EndX - line2StartX) * a) - ((line2EndY - line2StartY) * b);
    numerator2 = ((line1EndX - line1StartX) * a) - ((line1EndY - line1StartY) * b);
    a = numerator1 / denominator;
    b = numerator2 / denominator;

    // if we cast these lines infinitely in both directions, they intersect here:
    result.x = line1StartX + (a * (line1EndX - line1StartX));
    result.y = line1StartY + (a * (line1EndY - line1StartY));

    // if line1 is a segment and line2 is infinite, they intersect if:
    if (a > 0 && a < 1) {
        result.onLine1 = true;
    }
    // if line2 is a segment and line1 is infinite, they intersect if:
    if (b > 0 && b < 1) {
        result.onLine2 = true;
    }
    // if line1 and line2 are segments, they intersect if both of the above are true
    if (result.onLine1 && result.onLine2) {
        return [result.x, result.y];
    } else {
        return false;
    }
}

},{"turf-bearing":110,"turf-destination":111,"turf-distance":112,"turf-helpers":113}],110:[function(require,module,exports){
arguments[4][2][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-bearing":13,"dup":2}],111:[function(require,module,exports){
arguments[4][3][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-destination":55,"dup":3}],112:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],113:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],114:[function(require,module,exports){
var featureCollection = require('turf-helpers').featureCollection;
var centroid = require('turf-center');
var distance = require('turf-distance');
var inside = require('turf-inside');
var explode = require('turf-explode');

/**
 * Takes a feature and returns a {@link Point} guaranteed to be on the surface of the feature.
 *
 * * Given a {@link Polygon}, the point will be in the area of the polygon
 * * Given a {@link LineString}, the point will be along the string
 * * Given a {@link Point}, the point will the same as the input
 *
 * @param {(Feature|FeatureCollection)} fc any feature or set of features
 * @returns {Feature} a point on the surface of `input`
 * @example
 * // create a random polygon
 * var polygon = turf.random('polygon');
 *
 * //=polygon
 *
 * var pointOnPolygon = turf.pointOnSurface(polygon);
 *
* var resultFeatures = polygon.features.concat(pointOnPolygon);
* var result = {
*   "type": "FeatureCollection",
*   "features": resultFeatures
* };
 *
 * //=result
 */
function pointOnSurface(fc) {
    // normalize
    if (fc.type !== 'FeatureCollection') {
        if (fc.type !== 'Feature') {
            fc = {
                type: 'Feature',
                geometry: fc,
                properties: {}
            };
        }
        fc = featureCollection([fc]);
    }

    //get centroid
    var cent = centroid(fc);

    // check to see if centroid is on surface
    var onSurface = false;
    var i = 0;
    while (!onSurface && i < fc.features.length) {
        var geom = fc.features[i].geometry;
        var x, y, x1, y1, x2, y2, k;
        var onLine = false;
        if (geom.type === 'Point') {
            if (cent.geometry.coordinates[0] === geom.coordinates[0] &&
        cent.geometry.coordinates[1] === geom.coordinates[1]) {
                onSurface = true;
            }
        } else if (geom.type === 'MultiPoint') {
            var onMultiPoint = false;
            k = 0;
            while (!onMultiPoint && k < geom.coordinates.length) {
                if (cent.geometry.coordinates[0] === geom.coordinates[k][0] &&
          cent.geometry.coordinates[1] === geom.coordinates[k][1]) {
                    onSurface = true;
                    onMultiPoint = true;
                }
                k++;
            }
        } else if (geom.type === 'LineString') {
            k = 0;
            while (!onLine && k < geom.coordinates.length - 1) {
                x = cent.geometry.coordinates[0];
                y = cent.geometry.coordinates[1];
                x1 = geom.coordinates[k][0];
                y1 = geom.coordinates[k][1];
                x2 = geom.coordinates[k + 1][0];
                y2 = geom.coordinates[k + 1][1];
                if (pointOnSegment(x, y, x1, y1, x2, y2)) {
                    onLine = true;
                    onSurface = true;
                }
                k++;
            }
        } else if (geom.type === 'MultiLineString') {
            var j = 0;
            while (j < geom.coordinates.length) {
                onLine = false;
                k = 0;
                var line = geom.coordinates[j];
                while (!onLine && k < line.length - 1) {
                    x = cent.geometry.coordinates[0];
                    y = cent.geometry.coordinates[1];
                    x1 = line[k][0];
                    y1 = line[k][1];
                    x2 = line[k + 1][0];
                    y2 = line[k + 1][1];
                    if (pointOnSegment(x, y, x1, y1, x2, y2)) {
                        onLine = true;
                        onSurface = true;
                    }
                    k++;
                }
                j++;
            }
        } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
            var f = {
                type: 'Feature',
                geometry: geom,
                properties: {}
            };
            if (inside(cent, f)) {
                onSurface = true;
            }
        }
        i++;
    }
    if (onSurface) {
        return cent;
    } else {
        var vertices = featureCollection([]);
        for (i = 0; i < fc.features.length; i++) {
            vertices.features = vertices.features.concat(explode(fc.features[i]).features);
        }
        var closestVertex;
        var closestDistance = Infinity;
        for (i = 0; i < vertices.features.length; i++) {
            var dist = distance(cent, vertices.features[i], 'miles');
            if (dist < closestDistance) {
                closestDistance = dist;
                closestVertex = vertices.features[i];
            }
        }
        return closestVertex;
    }
}

function pointOnSegment(x, y, x1, y1, x2, y2) {
    var ab = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
    var ap = Math.sqrt((x - x1) * (x - x1) + (y - y1) * (y - y1));
    var pb = Math.sqrt((x2 - x) * (x2 - x) + (y2 - y) * (y2 - y));
    if (ab === ap + pb) {
        return true;
    }
}

module.exports = pointOnSurface;

},{"turf-center":115,"turf-distance":116,"turf-explode":117,"turf-helpers":118,"turf-inside":119}],115:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-center");

},{"/Users/tmcw/src/turf/packages/turf-center":22}],116:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],117:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-explode");

},{"/Users/tmcw/src/turf/packages/turf-explode":66}],118:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],119:[function(require,module,exports){
arguments[4][29][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-inside":75,"dup":29}],120:[function(require,module,exports){
var random = require('geojson-random');

/**
 * Generates random {@link GeoJSON} data, including {@link Point|Points} and {@link Polygon|Polygons}, for testing
 * and experimentation.
 *
 * @name random
 * @param {String} [type='point'] type of features desired: 'points' or 'polygons'
 * @param {Number} [count=1] how many geometries should be generated.
 * @param {Object} options options relevant to the feature desired. Can include:
 * @param {Array<number>} options.bbox a bounding box inside of which geometries
 * are placed. In the case of {@link Point} features, they are guaranteed to be within this bounds,
 * while {@link Polygon} features have their centroid within the bounds.
 * @param {Number} [options.num_vertices=10] options.vertices the number of vertices added
 * to polygon features.
 * @param {Number} [options.max_radial_length=10] the total number of decimal
 * degrees longitude or latitude that a polygon can extent outwards to
 * from its center.
 * @return {FeatureCollection} generated random features
 * @example
 * var points = turf.random('points', 100, {
 *   bbox: [-70, 40, -60, 60]
 * });
 *
 * //=points
 *
 * var polygons = turf.random('polygons', 4, {
 *   bbox: [-70, 40, -60, 60]
 * });
 *
 * //=polygons
 */
module.exports = function (type, count, options) {
    options = options || {};
    count = count || 1;
    switch (type) {
    case 'point':
    case 'points':
    case undefined:
        return random.point(count, options.bbox);
    case 'polygon':
    case 'polygons':
        return random.polygon(
                count,
                options.num_vertices,
                options.max_radial_length,
                options.bbox);
    default:
        throw new Error('Unknown type given: valid options are points and polygons');
    }
};

},{"geojson-random":121}],121:[function(require,module,exports){
module.exports = function() {
    throw new Error('call .point() or .polygon() instead');
};

function position(bbox) {
    if (bbox) return coordInBBBOX(bbox);
    else return [lon(), lat()];
}

module.exports.position = position;

module.exports.point = function(count, bbox) {
    var features = [];
    for (i = 0; i < count; i++) {
        features.push(feature(bbox ? point(position(bbox)) : point()));
    }
    return collection(features);
};

module.exports.polygon = function(count, num_vertices, max_radial_length, bbox) {
    if (typeof num_vertices !== 'number') num_vertices = 10;
    if (typeof max_radial_length !== 'number') max_radial_length = 10;
    var features = [];
    for (i = 0; i < count; i++) {
        var vertices = [],
            circle_offsets = Array.apply(null,
                new Array(num_vertices + 1)).map(Math.random);

        circle_offsets.forEach(sumOffsets);
        circle_offsets.forEach(scaleOffsets);
        vertices[vertices.length - 1] = vertices[0]; // close the ring

        // center the polygon around something
        vertices = vertices.map(vertexToCoordinate(position(bbox)));
        features.push(feature(polygon([vertices])));
    }

    function sumOffsets(cur, index, arr) {
        arr[index] = (index > 0) ? cur + arr[index - 1] : cur;
    }

    function scaleOffsets(cur, index) {
        cur = cur * 2 * Math.PI / circle_offsets[circle_offsets.length - 1];
        var radial_scaler = Math.random();
        vertices.push([
            radial_scaler * max_radial_length * Math.sin(cur),
            radial_scaler * max_radial_length * Math.cos(cur)
        ]);
    }

    return collection(features);
};


function vertexToCoordinate(hub) {
    return function(cur, index) { return [cur[0] + hub[0], cur[1] + hub[1]]; };
}

function rnd() { return Math.random() - 0.5; }
function lon() { return rnd() * 360; }
function lat() { return rnd() * 180; }

function point(coordinates) {
    return {
        type: 'Point',
        coordinates: coordinates || [lon(), lat()]
    };
}

function coordInBBBOX(bbox) {
    return [
        (Math.random() * (bbox[2] - bbox[0])) + bbox[0],
        (Math.random() * (bbox[3] - bbox[1])) + bbox[1]];
}

function pointInBBBOX() {
    return {
        type: 'Point',
        coordinates: [lon(), lat()]
    };
}

function polygon(coordinates) {
    return {
        type: 'Polygon',
        coordinates: coordinates
    };
}

function feature(geom) {
    return {
        type: 'Feature',
        geometry: geom,
        properties: {}
    };
}

function collection(f) {
    return {
        type: 'FeatureCollection',
        features: f
    };
}

},{}],122:[function(require,module,exports){
// http://stackoverflow.com/questions/11935175/sampling-a-random-subset-from-an-array
var featureCollection = require('turf-helpers').featureCollection;

/**
 * Takes a {@link FeatureCollection} and returns a FeatureCollection with given number of {@link Feature|features} at random.
 *
 * @name sample
 * @param {FeatureCollection} featurecollection set of input features
 * @param {number} num number of features to select
 * @return {FeatureCollection} a FeatureCollection with `n` features
 * @example
 * var points = turf.random('points', 1000);
 *
 * //=points
 *
 * var sample = turf.sample(points, 10);
 *
 * //=sample
 */
module.exports = function (featurecollection, num) {
    var outFC = featureCollection(getRandomSubarray(featurecollection.features, num));
    return outFC;
};

function getRandomSubarray(arr, size) {
    var shuffled = arr.slice(0), i = arr.length, min = i - size, temp, index;
    while (i-- > min) {
        index = Math.floor((i + 1) * Math.random());
        temp = shuffled[index];
        shuffled[index] = shuffled[i];
        shuffled[i] = temp;
    }
    return shuffled.slice(min);
}

},{"turf-helpers":123}],123:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],124:[function(require,module,exports){
var simplify = require('simplify-js');

// supported GeoJSON geometries, used to check whether to wrap in simpleFeature()
var supportedTypes = ['LineString', 'MultiLineString', 'Polygon', 'MultiPolygon'];

/**
 * Takes a {@link LineString} or {@link Polygon} and returns a simplified version. Internally uses [simplify-js](http://mourner.github.io/simplify-js/) to perform simplification.
 *
 * @name simplify
 * @param {Feature<(LineString|Polygon|MultiLineString|MultiPolygon)>|FeatureCollection|GeometryCollection} feature feature to be simplified
 * @param {number} tolerance simplification tolerance
 * @param {boolean} highQuality whether or not to spend more time to create
 * a higher-quality simplification with a different algorithm
 * @return {Feature<(LineString|Polygon|MultiLineString|MultiPolygon)>|FeatureCollection|GeometryCollection} a simplified feature
 * @example
  * var feature = {
 *   "type": "Feature",
 *   "properties": {},
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [-70.603637, -33.399918],
 *       [-70.614624, -33.395332],
 *       [-70.639343, -33.392466],
 *       [-70.659942, -33.394759],
 *       [-70.683975, -33.404504],
 *       [-70.697021, -33.419406],
 *       [-70.701141, -33.434306],
 *       [-70.700454, -33.446339],
 *       [-70.694274, -33.458369],
 *       [-70.682601, -33.465816],
 *       [-70.668869, -33.472117],
 *       [-70.646209, -33.473835],
 *       [-70.624923, -33.472117],
 *       [-70.609817, -33.468107],
 *       [-70.595397, -33.458369],
 *       [-70.587158, -33.442901],
 *       [-70.587158, -33.426283],
 *       [-70.590591, -33.414248],
 *       [-70.594711, -33.406224],
 *       [-70.603637, -33.399918]
 *     ]]
 *   }
 * };

 * var tolerance = 0.01;
 *
 * var simplified = turf.simplify(
 *  feature, tolerance, false);
 *
 * //=feature
 *
 * //=simplified
 */
module.exports = function (feature, tolerance, highQuality) {
    if (feature.type === 'Feature') {
        return simpleFeature(
            simplifyHelper(feature, tolerance, highQuality),
            feature.properties);
    } else if (feature.type === 'FeatureCollection') {
        return {
            type: 'FeatureCollection',
            features: feature.features.map(function (f) {
                var simplified = simplifyHelper(f, tolerance, highQuality);

                // we create simpleFeature here because it doesn't apply to GeometryCollection
                // so we can't create it at simplifyHelper()
                if (supportedTypes.indexOf(simplified.type) > -1) {
                    return simpleFeature(simplified, f.properties);
                } else {
                    return simplified;
                }
            })
        };
    } else if (feature.type === 'GeometryCollection') {
        return {
            type: 'GeometryCollection',
            geometries: feature.geometries.map(function (g) {
                if (supportedTypes.indexOf(g.type) > -1) {
                    return simplifyHelper({
                        type: 'Feature',
                        geometry: g
                    }, tolerance, highQuality);
                }
                return g;
            })
        };
    } else {
        return feature;
    }
};


function simplifyHelper(feature, tolerance, highQuality) {
    if (feature.geometry.type === 'LineString') {
        return {
            type: 'LineString',
            coordinates: simplifyLine(feature.geometry.coordinates, tolerance, highQuality)
        };
    } else if (feature.geometry.type === 'MultiLineString') {
        return {
            type: 'MultiLineString',
            coordinates: feature.geometry.coordinates.map(function (lines) {
                return simplifyLine(lines, tolerance, highQuality);
            })
        };
    } else if (feature.geometry.type === 'Polygon') {
        return {
            type: 'Polygon',
            coordinates: simplifyPolygon(feature.geometry.coordinates, tolerance, highQuality)
        };
    } else if (feature.geometry.type === 'MultiPolygon') {
        return {
            type: 'MultiPolygon',
            coordinates: feature.geometry.coordinates.map(function (rings) {
                return simplifyPolygon(rings, tolerance, highQuality);
            })
        };
    } else {
        // unsupported geometry type supplied
        return feature;
    }
}

/*
* returns true if ring's first coordinate is the same as its last
*/
function checkValidity(ring) {
    if (ring.length < 3) {
        return false;
    //if the last point is the same as the first, it's not a triangle
    } else if (ring.length === 3 &&
      ((ring[2][0] === ring[0][0]) && (ring[2][1] === ring[0][1]))) {
        return false;
    } else {
        return true;
    }
}

function simpleFeature(geom, properties) {
    return {
        type: 'Feature',
        geometry: geom,
        properties: properties
    };
}

function simplifyLine(coordinates, tolerance, highQuality) {
    return simplify(coordinates.map(function (coord) {
        return {x: coord[0], y: coord[1]};
    }), tolerance, highQuality).map(function (coords) {
        return [coords.x, coords.y];
    });
}

function simplifyPolygon(coordinates, tolerance, highQuality) {
    return coordinates.map(function (ring) {
        var pts = ring.map(function (coord) {
            return {x: coord[0], y: coord[1]};
        });
        if (pts.length < 4) {
            throw new Error('Invalid polygon');
        }
        var simpleRing = simplify(pts, tolerance, highQuality).map(function (coords) {
            return [coords.x, coords.y];
        });
        //remove 1 percent of tolerance until enough points to make a triangle
        while (!checkValidity(simpleRing)) {
            tolerance -= tolerance * 0.01;
            simpleRing = simplify(pts, tolerance, highQuality).map(function (coords) {
                return [coords.x, coords.y];
            });
        }
        if (
            (simpleRing[simpleRing.length - 1][0] !== simpleRing[0][0]) ||
                (simpleRing[simpleRing.length - 1][1] !== simpleRing[0][1])) {
            simpleRing.push(simpleRing[0]);
        }
        return simpleRing;
    });
}

},{"simplify-js":125}],125:[function(require,module,exports){
/*
 (c) 2013, Vladimir Agafonkin
 Simplify.js, a high-performance JS polyline simplification library
 mourner.github.io/simplify-js
*/

(function () { 'use strict';

// to suit your point format, run search/replace for '.x' and '.y';
// for 3D version, see 3d branch (configurability would draw significant performance overhead)

// square distance between 2 points
function getSqDist(p1, p2) {

    var dx = p1.x - p2.x,
        dy = p1.y - p2.y;

    return dx * dx + dy * dy;
}

// square distance from a point to a segment
function getSqSegDist(p, p1, p2) {

    var x = p1.x,
        y = p1.y,
        dx = p2.x - x,
        dy = p2.y - y;

    if (dx !== 0 || dy !== 0) {

        var t = ((p.x - x) * dx + (p.y - y) * dy) / (dx * dx + dy * dy);

        if (t > 1) {
            x = p2.x;
            y = p2.y;

        } else if (t > 0) {
            x += dx * t;
            y += dy * t;
        }
    }

    dx = p.x - x;
    dy = p.y - y;

    return dx * dx + dy * dy;
}
// rest of the code doesn't care about point format

// basic distance-based simplification
function simplifyRadialDist(points, sqTolerance) {

    var prevPoint = points[0],
        newPoints = [prevPoint],
        point;

    for (var i = 1, len = points.length; i < len; i++) {
        point = points[i];

        if (getSqDist(point, prevPoint) > sqTolerance) {
            newPoints.push(point);
            prevPoint = point;
        }
    }

    if (prevPoint !== point) newPoints.push(point);

    return newPoints;
}

// simplification using optimized Douglas-Peucker algorithm with recursion elimination
function simplifyDouglasPeucker(points, sqTolerance) {

    var len = points.length,
        MarkerArray = typeof Uint8Array !== 'undefined' ? Uint8Array : Array,
        markers = new MarkerArray(len),
        first = 0,
        last = len - 1,
        stack = [],
        newPoints = [],
        i, maxSqDist, sqDist, index;

    markers[first] = markers[last] = 1;

    while (last) {

        maxSqDist = 0;

        for (i = first + 1; i < last; i++) {
            sqDist = getSqSegDist(points[i], points[first], points[last]);

            if (sqDist > maxSqDist) {
                index = i;
                maxSqDist = sqDist;
            }
        }

        if (maxSqDist > sqTolerance) {
            markers[index] = 1;
            stack.push(first, index, index, last);
        }

        last = stack.pop();
        first = stack.pop();
    }

    for (i = 0; i < len; i++) {
        if (markers[i]) newPoints.push(points[i]);
    }

    return newPoints;
}

// both algorithms combined for awesome performance
function simplify(points, tolerance, highestQuality) {

    var sqTolerance = tolerance !== undefined ? tolerance * tolerance : 1;

    points = highestQuality ? points : simplifyRadialDist(points, sqTolerance);
    points = simplifyDouglasPeucker(points, sqTolerance);

    return points;
}

// export as AMD module / Node module / browser or worker variable
if (typeof define === 'function' && define.amd) define(function() { return simplify; });
else if (typeof module !== 'undefined') module.exports = simplify;
else if (typeof self !== 'undefined') self.simplify = simplify;
else window.simplify = simplify;

})();

},{}],126:[function(require,module,exports){
var featurecollection = require('turf-helpers').featureCollection;
var point = require('turf-helpers').point;
var polygon = require('turf-helpers').polygon;
var distance = require('turf-distance');

/**
 * Takes a bounding box and a cell depth and returns a set of square {@link Polygon|polygons} in a grid.
 *
 * @name squareGrid
 * @param {Array<number>} bbox extent in [minX, minY, maxX, maxY] order
 * @param {number} cellSize width of each cell
 * @param {string} units units to use for cellWidth
 * @return {FeatureCollection<Polygon>} grid a grid of polygons
 * @example
 * var extent = [-77.3876953125,38.71980474264239,-76.9482421875,39.027718840211605];
 * var cellWidth = 10;
 * var units = 'miles';
 *
 * var squareGrid = turf.squareGrid(extent, cellWidth, units);
 *
 * //=squareGrid
 */
module.exports = function squareGrid(bbox, cellSize, units) {
    var fc = featurecollection([]);
    var xFraction = cellSize / (distance(point([bbox[0], bbox[1]]), point([bbox[2], bbox[1]]), units));
    var cellWidth = xFraction * (bbox[2] - bbox[0]);
    var yFraction = cellSize / (distance(point([bbox[0], bbox[1]]), point([bbox[0], bbox[3]]), units));
    var cellHeight = yFraction * (bbox[3] - bbox[1]);

    var currentX = bbox[0];
    while (currentX <= bbox[2]) {
        var currentY = bbox[1];
        while (currentY <= bbox[3]) {
            var cellPoly = polygon([[
                [currentX, currentY],
                [currentX, currentY + cellHeight],
                [currentX + cellWidth, currentY + cellHeight],
                [currentX + cellWidth, currentY],
                [currentX, currentY]
            ]]);
            fc.features.push(cellPoly);

            currentY += cellHeight;
        }
        currentX += cellWidth;
    }

    return fc;
};

},{"turf-distance":127,"turf-helpers":128}],127:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],128:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],129:[function(require,module,exports){
var distance = require('turf-distance');

/**
 * Takes a bounding box and calculates the minimum square bounding box that
 * would contain the input.
 *
 * @name square
 * @param {Array<number>} bbox a bounding box
 * @return {Array<number>} a square surrounding `bbox`
 * @example
 * var bbox = [-20,-20,-15,0];
 *
 * var squared = turf.square(bbox);
 *
 * var features = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     turf.bboxPolygon(bbox),
 *     turf.bboxPolygon(squared)
 *   ]
 * };
 *
 * //=features
 */
module.exports = function (bbox) {
    var horizontalDistance = distance(bbox.slice(0, 2), [bbox[2], bbox[1]], 'miles');
    var verticalDistance = distance(bbox.slice(0, 2), [bbox[0], bbox[3]], 'miles');
    if (horizontalDistance >= verticalDistance) {
        var verticalMidpoint = (bbox[1] + bbox[3]) / 2;
        return [
            bbox[0],
            verticalMidpoint - ((bbox[2] - bbox[0]) / 2),
            bbox[2],
            verticalMidpoint + ((bbox[2] - bbox[0]) / 2)
        ];
    } else {
        var horizontalMidpoint = (bbox[0] + bbox[2]) / 2;
        return [
            horizontalMidpoint - ((bbox[3] - bbox[1]) / 2),
            bbox[1],
            horizontalMidpoint + ((bbox[3] - bbox[1]) / 2),
            bbox[3]
        ];
    }
};

},{"turf-distance":130}],130:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],131:[function(require,module,exports){
var inside = require('turf-inside');

/**
 * Takes a set of {@link Point|points} and a set of {@link Polygon|polygons} and performs a spatial join.
 *
 * @name tag
 * @param {FeatureCollection<Point>} points input points
 * @param {FeatureCollection<Polygon>} polygons input polygons
 * @param {string} field property in `polygons` to add to joined Point features
 * @param {string} outField property in `points` in which to store joined property from `polygons
 * @return {FeatureCollection<Point>} points with `containingPolyId` property containing values from `polyId`
 * @example
 * var bbox = [0, 0, 10, 10];
 * // create a triangular grid of polygons
 * var triangleGrid = turf.triangleGrid(bbox, 50, 'miles');
 * triangleGrid.features.forEach(function(f) {
 *   f.properties.fill = '#' +
 *     (~~(Math.random() * 16)).toString(16) +
 *     (~~(Math.random() * 16)).toString(16) +
 *     (~~(Math.random() * 16)).toString(16);
 *   f.properties.stroke = 0;
 *   f.properties['fill-opacity'] = 1;
 * });
 * var randomPoints = turf.random('point', 30, {
 *   bbox: bbox
 * });
 * var both = turf.featurecollection(
 *   triangleGrid.features.concat(randomPoints.features));
 *
 * //=both
 *
 * var tagged = turf.tag(randomPoints, triangleGrid,
 *                       'fill', 'marker-color');
 *
 * //=tagged
 */
module.exports = function (points, polygons, field, outField) {
    // prevent mutations
    points = JSON.parse(JSON.stringify(points));
    polygons = JSON.parse(JSON.stringify(polygons));
    points.features.forEach(function (pt) {
        if (!pt.properties) {
            pt.properties = {};
        }
        polygons.features.forEach(function (poly) {
            if (pt.properties[outField] === undefined) {
                var isInside = inside(pt, poly);
                if (isInside) {
                    pt.properties[outField] = poly.properties[field];
                }
            }
        });
    });
    return points;
};

},{"turf-inside":132}],132:[function(require,module,exports){
arguments[4][29][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-inside":75,"dup":29}],133:[function(require,module,exports){
var polygon = require('turf-helpers').polygon;
var earcut = require('earcut');

/**
 * Tesselates a {@link Feature<Polygon>} into a {@link FeatureCollection<Polygon>} of triangles
 * using [earcut](https://github.com/mapbox/earcut).
 *
 * @name tesselate
 * @param {Feature<Polygon>} polygon the polygon to tesselate
 * @returns {FeatureCollection<Polygon>} a geometrycollection feature
 * @example
 * var polygon = {"type":"Feature","id":"USA-CA","properties":{"name":"California"},"geometry":{"type":"Polygon","coordinates":[[[-123.233256,42.006186],[-122.378853,42.011663],[-121.037003,41.995232],[-120.001861,41.995232],[-119.996384,40.264519],[-120.001861,38.999346],[-118.71478,38.101128],[-117.498899,37.21934],[-116.540435,36.501861],[-115.85034,35.970598],[-114.634459,35.00118],[-114.634459,34.87521],[-114.470151,34.710902],[-114.333228,34.448009],[-114.136058,34.305608],[-114.256551,34.174162],[-114.415382,34.108438],[-114.535874,33.933176],[-114.497536,33.697668],[-114.524921,33.54979],[-114.727567,33.40739],[-114.661844,33.034958],[-114.524921,33.029481],[-114.470151,32.843265],[-114.524921,32.755634],[-114.72209,32.717295],[-116.04751,32.624187],[-117.126467,32.536556],[-117.24696,32.668003],[-117.252437,32.876127],[-117.329114,33.122589],[-117.471515,33.297851],[-117.7837,33.538836],[-118.183517,33.763391],[-118.260194,33.703145],[-118.413548,33.741483],[-118.391641,33.840068],[-118.566903,34.042715],[-118.802411,33.998899],[-119.218659,34.146777],[-119.278905,34.26727],[-119.558229,34.415147],[-119.875891,34.40967],[-120.138784,34.475393],[-120.472878,34.448009],[-120.64814,34.579455],[-120.609801,34.858779],[-120.670048,34.902595],[-120.631709,35.099764],[-120.894602,35.247642],[-120.905556,35.450289],[-121.004141,35.461243],[-121.168449,35.636505],[-121.283465,35.674843],[-121.332757,35.784382],[-121.716143,36.195153],[-121.896882,36.315645],[-121.935221,36.638785],[-121.858544,36.6114],[-121.787344,36.803093],[-121.929744,36.978355],[-122.105006,36.956447],[-122.335038,37.115279],[-122.417192,37.241248],[-122.400761,37.361741],[-122.515777,37.520572],[-122.515777,37.783465],[-122.329561,37.783465],[-122.406238,38.15042],[-122.488392,38.112082],[-122.504823,37.931343],[-122.701993,37.893004],[-122.937501,38.029928],[-122.97584,38.265436],[-123.129194,38.451652],[-123.331841,38.566668],[-123.44138,38.698114],[-123.737134,38.95553],[-123.687842,39.032208],[-123.824765,39.366301],[-123.764519,39.552517],[-123.85215,39.831841],[-124.109566,40.105688],[-124.361506,40.259042],[-124.410798,40.439781],[-124.158859,40.877937],[-124.109566,41.025814],[-124.158859,41.14083],[-124.065751,41.442061],[-124.147905,41.715908],[-124.257444,41.781632],[-124.213628,42.000709],[-123.233256,42.006186]]]}};
 *
 * var triangles = turf.tesselate(polygon);
 *
 * //=triangles
 */

module.exports = function (poly) {
    if (!poly.geometry || (poly.geometry.type !== 'Polygon' && poly.geometry.type !== 'MultiPolygon')) {
        throw new Error('input must be a Polygon or MultiPolygon');
    }

    var fc = {type: 'FeatureCollection', features: []};

    if (poly.geometry.type === 'Polygon') {
        fc.features = processPolygon(poly.geometry.coordinates);
    } else {
        poly.geometry.coordinates.forEach(function (coordinates) {
            fc.features = fc.features.concat(processPolygon(coordinates));
        });
    }

    return fc;
};

function processPolygon(coordinates) {
    var data = flattenCoords(coordinates);
    var dim = 2;
    var result = earcut(data.vertices, data.holes, dim);

    var features = [];
    var vertices = [];

    result.forEach(function (vert, i) {
        var index = result[i];
        vertices.push([data.vertices[index * dim], data.vertices[index * dim + 1]]);
    });

    for (var i = 0; i < vertices.length; i += 3) {
        var coords = vertices.slice(i, i + 3);
        coords.push(vertices[i]);
        features.push(polygon([coords]));
    }

    return features;
}

function flattenCoords(data) {
    var dim = data[0][0].length,
        result = {vertices: [], holes: [], dimensions: dim},
        holeIndex = 0;

    for (var i = 0; i < data.length; i++) {
        for (var j = 0; j < data[i].length; j++) {
            for (var d = 0; d < dim; d++) result.vertices.push(data[i][j][d]);
        }
        if (i > 0) {
            holeIndex += data[i - 1].length;
            result.holes.push(holeIndex);
        }
    }

    return result;
}

},{"earcut":134,"turf-helpers":135}],134:[function(require,module,exports){
'use strict';

module.exports = earcut;

function earcut(data, holeIndices, dim) {

    dim = dim || 2;

    var hasHoles = holeIndices && holeIndices.length,
        outerLen = hasHoles ? holeIndices[0] * dim : data.length,
        outerNode = linkedList(data, 0, outerLen, dim, true),
        triangles = [];

    if (!outerNode) return triangles;

    var minX, minY, maxX, maxY, x, y, size;

    if (hasHoles) outerNode = eliminateHoles(data, holeIndices, outerNode, dim);

    // if the shape is not too simple, we'll use z-order curve hash later; calculate polygon bbox
    if (data.length > 80 * dim) {
        minX = maxX = data[0];
        minY = maxY = data[1];

        for (var i = dim; i < outerLen; i += dim) {
            x = data[i];
            y = data[i + 1];
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
        }

        // minX, minY and size are later used to transform coords into integers for z-order calculation
        size = Math.max(maxX - minX, maxY - minY);
    }

    earcutLinked(outerNode, triangles, dim, minX, minY, size);

    return triangles;
}

// create a circular doubly linked list from polygon points in the specified winding order
function linkedList(data, start, end, dim, clockwise) {
    var i, last;

    if (clockwise === (signedArea(data, start, end, dim) > 0)) {
        for (i = start; i < end; i += dim) last = insertNode(i, data[i], data[i + 1], last);
    } else {
        for (i = end - dim; i >= start; i -= dim) last = insertNode(i, data[i], data[i + 1], last);
    }

    if (last && equals(last, last.next)) {
        removeNode(last);
        last = last.next;
    }

    return last;
}

// eliminate colinear or duplicate points
function filterPoints(start, end) {
    if (!start) return start;
    if (!end) end = start;

    var p = start,
        again;
    do {
        again = false;

        if (!p.steiner && (equals(p, p.next) || area(p.prev, p, p.next) === 0)) {
            removeNode(p);
            p = end = p.prev;
            if (p === p.next) return null;
            again = true;

        } else {
            p = p.next;
        }
    } while (again || p !== end);

    return end;
}

// main ear slicing loop which triangulates a polygon (given as a linked list)
function earcutLinked(ear, triangles, dim, minX, minY, size, pass) {
    if (!ear) return;

    // interlink polygon nodes in z-order
    if (!pass && size) indexCurve(ear, minX, minY, size);

    var stop = ear,
        prev, next;

    // iterate through ears, slicing them one by one
    while (ear.prev !== ear.next) {
        prev = ear.prev;
        next = ear.next;

        if (size ? isEarHashed(ear, minX, minY, size) : isEar(ear)) {
            // cut off the triangle
            triangles.push(prev.i / dim);
            triangles.push(ear.i / dim);
            triangles.push(next.i / dim);

            removeNode(ear);

            // skipping the next vertice leads to less sliver triangles
            ear = next.next;
            stop = next.next;

            continue;
        }

        ear = next;

        // if we looped through the whole remaining polygon and can't find any more ears
        if (ear === stop) {
            // try filtering points and slicing again
            if (!pass) {
                earcutLinked(filterPoints(ear), triangles, dim, minX, minY, size, 1);

            // if this didn't work, try curing all small self-intersections locally
            } else if (pass === 1) {
                ear = cureLocalIntersections(ear, triangles, dim);
                earcutLinked(ear, triangles, dim, minX, minY, size, 2);

            // as a last resort, try splitting the remaining polygon into two
            } else if (pass === 2) {
                splitEarcut(ear, triangles, dim, minX, minY, size);
            }

            break;
        }
    }
}

// check whether a polygon node forms a valid ear with adjacent nodes
function isEar(ear) {
    var a = ear.prev,
        b = ear,
        c = ear.next;

    if (area(a, b, c) >= 0) return false; // reflex, can't be an ear

    // now make sure we don't have other points inside the potential ear
    var p = ear.next.next;

    while (p !== ear.prev) {
        if (pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) &&
            area(p.prev, p, p.next) >= 0) return false;
        p = p.next;
    }

    return true;
}

function isEarHashed(ear, minX, minY, size) {
    var a = ear.prev,
        b = ear,
        c = ear.next;

    if (area(a, b, c) >= 0) return false; // reflex, can't be an ear

    // triangle bbox; min & max are calculated like this for speed
    var minTX = a.x < b.x ? (a.x < c.x ? a.x : c.x) : (b.x < c.x ? b.x : c.x),
        minTY = a.y < b.y ? (a.y < c.y ? a.y : c.y) : (b.y < c.y ? b.y : c.y),
        maxTX = a.x > b.x ? (a.x > c.x ? a.x : c.x) : (b.x > c.x ? b.x : c.x),
        maxTY = a.y > b.y ? (a.y > c.y ? a.y : c.y) : (b.y > c.y ? b.y : c.y);

    // z-order range for the current triangle bbox;
    var minZ = zOrder(minTX, minTY, minX, minY, size),
        maxZ = zOrder(maxTX, maxTY, minX, minY, size);

    // first look for points inside the triangle in increasing z-order
    var p = ear.nextZ;

    while (p && p.z <= maxZ) {
        if (p !== ear.prev && p !== ear.next &&
            pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) &&
            area(p.prev, p, p.next) >= 0) return false;
        p = p.nextZ;
    }

    // then look for points in decreasing z-order
    p = ear.prevZ;

    while (p && p.z >= minZ) {
        if (p !== ear.prev && p !== ear.next &&
            pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) &&
            area(p.prev, p, p.next) >= 0) return false;
        p = p.prevZ;
    }

    return true;
}

// go through all polygon nodes and cure small local self-intersections
function cureLocalIntersections(start, triangles, dim) {
    var p = start;
    do {
        var a = p.prev,
            b = p.next.next;

        if (!equals(a, b) && intersects(a, p, p.next, b) && locallyInside(a, b) && locallyInside(b, a)) {

            triangles.push(a.i / dim);
            triangles.push(p.i / dim);
            triangles.push(b.i / dim);

            // remove two nodes involved
            removeNode(p);
            removeNode(p.next);

            p = start = b;
        }
        p = p.next;
    } while (p !== start);

    return p;
}

// try splitting polygon into two and triangulate them independently
function splitEarcut(start, triangles, dim, minX, minY, size) {
    // look for a valid diagonal that divides the polygon into two
    var a = start;
    do {
        var b = a.next.next;
        while (b !== a.prev) {
            if (a.i !== b.i && isValidDiagonal(a, b)) {
                // split the polygon in two by the diagonal
                var c = splitPolygon(a, b);

                // filter colinear points around the cuts
                a = filterPoints(a, a.next);
                c = filterPoints(c, c.next);

                // run earcut on each half
                earcutLinked(a, triangles, dim, minX, minY, size);
                earcutLinked(c, triangles, dim, minX, minY, size);
                return;
            }
            b = b.next;
        }
        a = a.next;
    } while (a !== start);
}

// link every hole into the outer loop, producing a single-ring polygon without holes
function eliminateHoles(data, holeIndices, outerNode, dim) {
    var queue = [],
        i, len, start, end, list;

    for (i = 0, len = holeIndices.length; i < len; i++) {
        start = holeIndices[i] * dim;
        end = i < len - 1 ? holeIndices[i + 1] * dim : data.length;
        list = linkedList(data, start, end, dim, false);
        if (list === list.next) list.steiner = true;
        queue.push(getLeftmost(list));
    }

    queue.sort(compareX);

    // process holes from left to right
    for (i = 0; i < queue.length; i++) {
        eliminateHole(queue[i], outerNode);
        outerNode = filterPoints(outerNode, outerNode.next);
    }

    return outerNode;
}

function compareX(a, b) {
    return a.x - b.x;
}

// find a bridge between vertices that connects hole with an outer ring and and link it
function eliminateHole(hole, outerNode) {
    outerNode = findHoleBridge(hole, outerNode);
    if (outerNode) {
        var b = splitPolygon(outerNode, hole);
        filterPoints(b, b.next);
    }
}

// David Eberly's algorithm for finding a bridge between hole and outer polygon
function findHoleBridge(hole, outerNode) {
    var p = outerNode,
        hx = hole.x,
        hy = hole.y,
        qx = -Infinity,
        m;

    // find a segment intersected by a ray from the hole's leftmost point to the left;
    // segment's endpoint with lesser x will be potential connection point
    do {
        if (hy <= p.y && hy >= p.next.y) {
            var x = p.x + (hy - p.y) * (p.next.x - p.x) / (p.next.y - p.y);
            if (x <= hx && x > qx) {
                qx = x;
                if (x === hx) {
                    if (hy === p.y) return p;
                    if (hy === p.next.y) return p.next;
                }
                m = p.x < p.next.x ? p : p.next;
            }
        }
        p = p.next;
    } while (p !== outerNode);

    if (!m) return null;

    if (hx === qx) return m.prev; // hole touches outer segment; pick lower endpoint

    // look for points inside the triangle of hole point, segment intersection and endpoint;
    // if there are no points found, we have a valid connection;
    // otherwise choose the point of the minimum angle with the ray as connection point

    var stop = m,
        mx = m.x,
        my = m.y,
        tanMin = Infinity,
        tan;

    p = m.next;

    while (p !== stop) {
        if (hx >= p.x && p.x >= mx &&
                pointInTriangle(hy < my ? hx : qx, hy, mx, my, hy < my ? qx : hx, hy, p.x, p.y)) {

            tan = Math.abs(hy - p.y) / (hx - p.x); // tangential

            if ((tan < tanMin || (tan === tanMin && p.x > m.x)) && locallyInside(p, hole)) {
                m = p;
                tanMin = tan;
            }
        }

        p = p.next;
    }

    return m;
}

// interlink polygon nodes in z-order
function indexCurve(start, minX, minY, size) {
    var p = start;
    do {
        if (p.z === null) p.z = zOrder(p.x, p.y, minX, minY, size);
        p.prevZ = p.prev;
        p.nextZ = p.next;
        p = p.next;
    } while (p !== start);

    p.prevZ.nextZ = null;
    p.prevZ = null;

    sortLinked(p);
}

// Simon Tatham's linked list merge sort algorithm
// http://www.chiark.greenend.org.uk/~sgtatham/algorithms/listsort.html
function sortLinked(list) {
    var i, p, q, e, tail, numMerges, pSize, qSize,
        inSize = 1;

    do {
        p = list;
        list = null;
        tail = null;
        numMerges = 0;

        while (p) {
            numMerges++;
            q = p;
            pSize = 0;
            for (i = 0; i < inSize; i++) {
                pSize++;
                q = q.nextZ;
                if (!q) break;
            }

            qSize = inSize;

            while (pSize > 0 || (qSize > 0 && q)) {

                if (pSize === 0) {
                    e = q;
                    q = q.nextZ;
                    qSize--;
                } else if (qSize === 0 || !q) {
                    e = p;
                    p = p.nextZ;
                    pSize--;
                } else if (p.z <= q.z) {
                    e = p;
                    p = p.nextZ;
                    pSize--;
                } else {
                    e = q;
                    q = q.nextZ;
                    qSize--;
                }

                if (tail) tail.nextZ = e;
                else list = e;

                e.prevZ = tail;
                tail = e;
            }

            p = q;
        }

        tail.nextZ = null;
        inSize *= 2;

    } while (numMerges > 1);

    return list;
}

// z-order of a point given coords and size of the data bounding box
function zOrder(x, y, minX, minY, size) {
    // coords are transformed into non-negative 15-bit integer range
    x = 32767 * (x - minX) / size;
    y = 32767 * (y - minY) / size;

    x = (x | (x << 8)) & 0x00FF00FF;
    x = (x | (x << 4)) & 0x0F0F0F0F;
    x = (x | (x << 2)) & 0x33333333;
    x = (x | (x << 1)) & 0x55555555;

    y = (y | (y << 8)) & 0x00FF00FF;
    y = (y | (y << 4)) & 0x0F0F0F0F;
    y = (y | (y << 2)) & 0x33333333;
    y = (y | (y << 1)) & 0x55555555;

    return x | (y << 1);
}

// find the leftmost node of a polygon ring
function getLeftmost(start) {
    var p = start,
        leftmost = start;
    do {
        if (p.x < leftmost.x) leftmost = p;
        p = p.next;
    } while (p !== start);

    return leftmost;
}

// check if a point lies within a convex triangle
function pointInTriangle(ax, ay, bx, by, cx, cy, px, py) {
    return (cx - px) * (ay - py) - (ax - px) * (cy - py) >= 0 &&
           (ax - px) * (by - py) - (bx - px) * (ay - py) >= 0 &&
           (bx - px) * (cy - py) - (cx - px) * (by - py) >= 0;
}

// check if a diagonal between two polygon nodes is valid (lies in polygon interior)
function isValidDiagonal(a, b) {
    return a.next.i !== b.i && a.prev.i !== b.i && !intersectsPolygon(a, b) &&
           locallyInside(a, b) && locallyInside(b, a) && middleInside(a, b);
}

// signed area of a triangle
function area(p, q, r) {
    return (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
}

// check if two points are equal
function equals(p1, p2) {
    return p1.x === p2.x && p1.y === p2.y;
}

// check if two segments intersect
function intersects(p1, q1, p2, q2) {
    if ((equals(p1, q1) && equals(p2, q2)) ||
        (equals(p1, q2) && equals(p2, q1))) return true;
    return area(p1, q1, p2) > 0 !== area(p1, q1, q2) > 0 &&
           area(p2, q2, p1) > 0 !== area(p2, q2, q1) > 0;
}

// check if a polygon diagonal intersects any polygon segments
function intersectsPolygon(a, b) {
    var p = a;
    do {
        if (p.i !== a.i && p.next.i !== a.i && p.i !== b.i && p.next.i !== b.i &&
                intersects(p, p.next, a, b)) return true;
        p = p.next;
    } while (p !== a);

    return false;
}

// check if a polygon diagonal is locally inside the polygon
function locallyInside(a, b) {
    return area(a.prev, a, a.next) < 0 ?
        area(a, b, a.next) >= 0 && area(a, a.prev, b) >= 0 :
        area(a, b, a.prev) < 0 || area(a, a.next, b) < 0;
}

// check if the middle point of a polygon diagonal is inside the polygon
function middleInside(a, b) {
    var p = a,
        inside = false,
        px = (a.x + b.x) / 2,
        py = (a.y + b.y) / 2;
    do {
        if (((p.y > py) !== (p.next.y > py)) && (px < (p.next.x - p.x) * (py - p.y) / (p.next.y - p.y) + p.x))
            inside = !inside;
        p = p.next;
    } while (p !== a);

    return inside;
}

// link two polygon vertices with a bridge; if the vertices belong to the same ring, it splits polygon into two;
// if one belongs to the outer ring and another to a hole, it merges it into a single ring
function splitPolygon(a, b) {
    var a2 = new Node(a.i, a.x, a.y),
        b2 = new Node(b.i, b.x, b.y),
        an = a.next,
        bp = b.prev;

    a.next = b;
    b.prev = a;

    a2.next = an;
    an.prev = a2;

    b2.next = a2;
    a2.prev = b2;

    bp.next = b2;
    b2.prev = bp;

    return b2;
}

// create a node and optionally link it with previous one (in a circular doubly linked list)
function insertNode(i, x, y, last) {
    var p = new Node(i, x, y);

    if (!last) {
        p.prev = p;
        p.next = p;

    } else {
        p.next = last.next;
        p.prev = last;
        last.next.prev = p;
        last.next = p;
    }
    return p;
}

function removeNode(p) {
    p.next.prev = p.prev;
    p.prev.next = p.next;

    if (p.prevZ) p.prevZ.nextZ = p.nextZ;
    if (p.nextZ) p.nextZ.prevZ = p.prevZ;
}

function Node(i, x, y) {
    // vertice index in coordinates array
    this.i = i;

    // vertex coordinates
    this.x = x;
    this.y = y;

    // previous and next vertice nodes in a polygon ring
    this.prev = null;
    this.next = null;

    // z-order curve value
    this.z = null;

    // previous and next nodes in z-order
    this.prevZ = null;
    this.nextZ = null;

    // indicates whether this is a steiner point
    this.steiner = false;
}

// return a percentage difference between the polygon area and its triangulation area;
// used to verify correctness of triangulation
earcut.deviation = function (data, holeIndices, dim, triangles) {
    var hasHoles = holeIndices && holeIndices.length;
    var outerLen = hasHoles ? holeIndices[0] * dim : data.length;

    var polygonArea = Math.abs(signedArea(data, 0, outerLen, dim));
    if (hasHoles) {
        for (var i = 0, len = holeIndices.length; i < len; i++) {
            var start = holeIndices[i] * dim;
            var end = i < len - 1 ? holeIndices[i + 1] * dim : data.length;
            polygonArea -= Math.abs(signedArea(data, start, end, dim));
        }
    }

    var trianglesArea = 0;
    for (i = 0; i < triangles.length; i += 3) {
        var a = triangles[i] * dim;
        var b = triangles[i + 1] * dim;
        var c = triangles[i + 2] * dim;
        trianglesArea += Math.abs(
            (data[a] - data[c]) * (data[b + 1] - data[a + 1]) -
            (data[a] - data[b]) * (data[c + 1] - data[a + 1]));
    }

    return polygonArea === 0 && trianglesArea === 0 ? 0 :
        Math.abs((trianglesArea - polygonArea) / polygonArea);
};

function signedArea(data, start, end, dim) {
    var sum = 0;
    for (var i = start, j = end - dim; i < end; i += dim) {
        sum += (data[j] - data[i]) * (data[i + 1] + data[j + 1]);
        j = i;
    }
    return sum;
}

// turn a polygon in a multi-dimensional array form (e.g. as in GeoJSON) into a form Earcut accepts
earcut.flatten = function (data) {
    var dim = data[0][0].length,
        result = {vertices: [], holes: [], dimensions: dim},
        holeIndex = 0;

    for (var i = 0; i < data.length; i++) {
        for (var j = 0; j < data[i].length; j++) {
            for (var d = 0; d < dim; d++) result.vertices.push(data[i][j][d]);
        }
        if (i > 0) {
            holeIndex += data[i - 1].length;
            result.holes.push(holeIndex);
        }
    }
    return result;
};

},{}],135:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],136:[function(require,module,exports){
//http://en.wikipedia.org/wiki/Delaunay_triangulation
//https://github.com/ironwallaby/delaunay
var polygon = require('turf-helpers').polygon;
var featurecollection = require('turf-helpers').featureCollection;

/**
 * Takes a set of {@link Point|points} and the name of a z-value property and
 * creates a [Triangulated Irregular Network](http://en.wikipedia.org/wiki/Triangulated_irregular_network),
 * or a TIN for short, returned as a collection of Polygons. These are often used
 * for developing elevation contour maps or stepped heat visualizations.
 *
 * This triangulates the points, as well as adds properties called `a`, `b`,
 * and `c` representing the value of the given `propertyName` at each of
 * the points that represent the corners of the triangle.
 *
 * @name tin
 * @param {FeatureCollection<Point>} points input points
 * @param {String=} z name of the property from which to pull z values
 * This is optional: if not given, then there will be no extra data added to the derived triangles.
 * @return {FeatureCollection<Polygon>} TIN output
 * @example
 * // generate some random point data
 * var points = turf.random('points', 30, {
 *   bbox: [50, 30, 70, 50]
 * });
 * //=points
 * // add a random property to each point between 0 and 9
 * for (var i = 0; i < points.features.length; i++) {
 *   points.features[i].properties.z = ~~(Math.random() * 9);
 * }
 * var tin = turf.tin(points, 'z')
 * for (var i = 0; i < tin.features.length; i++) {
 *   var properties  = tin.features[i].properties;
 *   // roughly turn the properties of each
 *   // triangle into a fill color
 *   // so we can visualize the result
 *   properties.fill = '#' + properties.a +
 *     properties.b + properties.c;
 * }
 * //=tin
 */
module.exports = function (points, z) {
    //break down points
    return featurecollection(triangulate(points.features.map(function (p) {
        var point = {
            x: p.geometry.coordinates[0],
            y: p.geometry.coordinates[1]
        };
        if (z) point.z = p.properties[z];
        return point;
    })).map(function (triangle) {
        return polygon([[
        [triangle.a.x, triangle.a.y],
        [triangle.b.x, triangle.b.y],
        [triangle.c.x, triangle.c.y],
        [triangle.a.x, triangle.a.y]
        ]], {
            a: triangle.a.z,
            b: triangle.b.z,
            c: triangle.c.z
        });
    }));
};

function Triangle(a, b, c) {
    this.a = a;
    this.b = b;
    this.c = c;

    var A = b.x - a.x,
        B = b.y - a.y,
        C = c.x - a.x,
        D = c.y - a.y,
        E = A * (a.x + b.x) + B * (a.y + b.y),
        F = C * (a.x + c.x) + D * (a.y + c.y),
        G = 2 * (A * (c.y - b.y) - B * (c.x - b.x)),
        minx, miny, dx, dy;

    // If the points of the triangle are collinear, then just find the
    // extremes and use the midpoint as the center of the circumcircle.
    if (Math.abs(G) < 0.000001) {
        minx = Math.min(a.x, b.x, c.x);
        miny = Math.min(a.y, b.y, c.y);
        dx = (Math.max(a.x, b.x, c.x) - minx) * 0.5;
        dy = (Math.max(a.y, b.y, c.y) - miny) * 0.5;

        this.x = minx + dx;
        this.y = miny + dy;
        this.r = dx * dx + dy * dy;
    } else {
        this.x = (D * E - B * F) / G;
        this.y = (A * F - C * E) / G;
        dx = this.x - a.x;
        dy = this.y - a.y;
        this.r = dx * dx + dy * dy;
    }
}

function byX(a, b) {
    return b.x - a.x;
}

function dedup(edges) {
    var j = edges.length,
        a, b, i, m, n;

    outer:
  while (j) {
      b = edges[--j];
      a = edges[--j];
      i = j;
      while (i) {
          n = edges[--i];
          m = edges[--i];
          if ((a === m && b === n) || (a === n && b === m)) {
              edges.splice(j, 2);
              edges.splice(i, 2);
              j -= 2;
              continue outer;
          }
      }
  }
}

function triangulate(vertices) {
    // Bail if there aren't enough vertices to form any triangles.
    if (vertices.length < 3)
        return [];

    // Ensure the vertex array is in order of descending X coordinate
    // (which is needed to ensure a subquadratic runtime), and then find
    // the bounding box around the points.
    vertices.sort(byX);

    var i = vertices.length - 1,
        xmin = vertices[i].x,
        xmax = vertices[0].x,
        ymin = vertices[i].y,
        ymax = ymin;

    while (i--) {
        if (vertices[i].y < ymin)
            ymin = vertices[i].y;
        if (vertices[i].y > ymax)
            ymax = vertices[i].y;
    }

    //Find a supertriangle, which is a triangle that surrounds all the
    //vertices. This is used like something of a sentinel value to remove
    //cases in the main algorithm, and is removed before we return any
    // results.

    // Once found, put it in the "open" list. (The "open" list is for
    // triangles who may still need to be considered; the "closed" list is
    // for triangles which do not.)
    var dx = xmax - xmin,
        dy = ymax - ymin,
        dmax = (dx > dy) ? dx : dy,
        xmid = (xmax + xmin) * 0.5,
        ymid = (ymax + ymin) * 0.5,
        open = [
            new Triangle({
                x: xmid - 20 * dmax,
                y: ymid - dmax,
                __sentinel: true
            }, {
                x: xmid,
                y: ymid + 20 * dmax,
                __sentinel: true
            }, {
                x: xmid + 20 * dmax,
                y: ymid - dmax,
                __sentinel: true
            }
        )],
        closed = [],
        edges = [],
        j, a, b;

    // Incrementally add each vertex to the mesh.
    i = vertices.length;
    while (i--) {
        // For each open triangle, check to see if the current point is
        // inside it's circumcircle. If it is, remove the triangle and add
        // it's edges to an edge list.
        edges.length = 0;
        j = open.length;
        while (j--) {
            // If this point is to the right of this triangle's circumcircle,
            // then this triangle should never get checked again. Remove it
            // from the open list, add it to the closed list, and skip.
            dx = vertices[i].x - open[j].x;
            if (dx > 0 && dx * dx > open[j].r) {
                closed.push(open[j]);
                open.splice(j, 1);
                continue;
            }

            // If not, skip this triangle.
            dy = vertices[i].y - open[j].y;
            if (dx * dx + dy * dy > open[j].r)
                continue;

            // Remove the triangle and add it's edges to the edge list.
            edges.push(
        open[j].a, open[j].b,
        open[j].b, open[j].c,
        open[j].c, open[j].a
      );
            open.splice(j, 1);
        }

        // Remove any doubled edges.
        dedup(edges);

        // Add a new triangle for each edge.
        j = edges.length;
        while (j) {
            b = edges[--j];
            a = edges[--j];
            open.push(new Triangle(a, b, vertices[i]));
        }
    }

    // Copy any remaining open triangles to the closed list, and then
    // remove any triangles that share a vertex with the supertriangle.
    Array.prototype.push.apply(closed, open);

    i = closed.length;
    while (i--)
        if (closed[i].a.__sentinel ||
      closed[i].b.__sentinel ||
      closed[i].c.__sentinel)
            closed.splice(i, 1);

    return closed;
}

},{"turf-helpers":137}],137:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],138:[function(require,module,exports){
var featurecollection = require('turf-helpers').featureCollection;
var polygon = require('turf-helpers').polygon;
var distance = require('turf-distance');

/**
 * Takes a bounding box and a cell depth and returns a set of triangular {@link Polygon|polygons} in a grid.
 *
 * @name triangleGrid
 * @param {Array<number>} bbox extent in [minX, minY, maxX, maxY] order
 * @param {number} cellSize dimension of each cell
 * @param {string} units units to use for cellWidth
 * @return {FeatureCollection<Polygon>} grid of polygons
 * @example
 * var extent = [-77.3876953125,38.71980474264239,-76.9482421875,39.027718840211605];
 * var cellWidth = 10;
 * var units = 'miles';
 *
 * var triangleGrid = turf.triangleGrid(extent, cellWidth, units);
 *
 * //=triangleGrid
 */
module.exports = function (bbox, cellSize, units) {
    var fc = featurecollection([]);
    var xFraction = cellSize / (distance([bbox[0], bbox[1]], [bbox[2], bbox[1]], units));
    var cellWidth = xFraction * (bbox[2] - bbox[0]);
    var yFraction = cellSize / (distance([bbox[0], bbox[1]], [bbox[0], bbox[3]], units));
    var cellHeight = yFraction * (bbox[3] - bbox[1]);

    var xi = 0;
    var currentX = bbox[0];
    while (currentX <= bbox[2]) {
        var yi = 0;
        var currentY = bbox[1];
        while (currentY <= bbox[3]) {
            if (xi % 2 === 0 && yi % 2 === 0) {
                fc.features.push(polygon([[
                    [currentX, currentY],
                    [currentX, currentY + cellHeight],
                    [currentX + cellWidth, currentY],
                    [currentX, currentY]
                ]]), polygon([[
                    [currentX, currentY + cellHeight],
                    [currentX + cellWidth, currentY + cellHeight],
                    [currentX + cellWidth, currentY],
                    [currentX, currentY + cellHeight]
                ]]));
            } else if (xi % 2 === 0 && yi % 2 === 1) {
                fc.features.push(polygon([[
                    [currentX, currentY],
                    [currentX + cellWidth, currentY + cellHeight],
                    [currentX + cellWidth, currentY],
                    [currentX, currentY]
                ]]), polygon([[
                    [currentX, currentY],
                    [currentX, currentY + cellHeight],
                    [currentX + cellWidth, currentY + cellHeight],
                    [currentX, currentY]
                ]]));
            } else if (yi % 2 === 0 && xi % 2 === 1) {
                fc.features.push(polygon([[
                    [currentX, currentY],
                    [currentX, currentY + cellHeight],
                    [currentX + cellWidth, currentY + cellHeight],
                    [currentX, currentY]
                ]]), polygon([[
                    [currentX, currentY],
                    [currentX + cellWidth, currentY + cellHeight],
                    [currentX + cellWidth, currentY],
                    [currentX, currentY]
                ]]));
            } else if (yi % 2 === 1 && xi % 2 === 1) {
                fc.features.push(polygon([[
                    [currentX, currentY],
                    [currentX, currentY + cellHeight],
                    [currentX + cellWidth, currentY],
                    [currentX, currentY]
                ]]), polygon([[
                    [currentX, currentY + cellHeight],
                    [currentX + cellWidth, currentY + cellHeight],
                    [currentX + cellWidth, currentY],
                    [currentX, currentY + cellHeight]
                ]]));
            }
            currentY += cellHeight;
            yi++;
        }
        xi++;
        currentX += cellWidth;
    }
    return fc;
};


},{"turf-distance":139,"turf-helpers":140}],139:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],140:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],141:[function(require,module,exports){
// look here for help http://svn.osgeo.org/grass/grass/branches/releasebranch_6_4/vector/v.overlay/main.c
//must be array of polygons

// depend on jsts for now https://github.com/bjornharrtell/jsts/blob/master/examples/overlay.html

var jsts = require('jsts');

/**
 * Takes two {@link Polygon|polygons} and returns a combined polygon. If the input polygons are not contiguous, this function returns a {@link MultiPolygon} feature.
 *
 * @name union
 * @param {Feature<Polygon>} poly1 input polygon
 * @param {Feature<Polygon>} poly2 another input polygon
 * @return {Feature<(Polygon|MultiPolygon)>} a combined {@link Polygon} or {@link MultiPolygon} feature
 * @example
 * var poly1 = {
 *   "type": "Feature",
 *   "properties": {
 *     "fill": "#0f0"
 *   },
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [-82.574787, 35.594087],
 *       [-82.574787, 35.615581],
 *       [-82.545261, 35.615581],
 *       [-82.545261, 35.594087],
 *       [-82.574787, 35.594087]
 *     ]]
 *   }
 * };
 * var poly2 = {
 *   "type": "Feature",
 *   "properties": {
 *     "fill": "#00f"
 *   },
 *   "geometry": {
 *     "type": "Polygon",
 *     "coordinates": [[
 *       [-82.560024, 35.585153],
 *       [-82.560024, 35.602602],
 *       [-82.52964, 35.602602],
 *       [-82.52964, 35.585153],
 *       [-82.560024, 35.585153]
 *     ]]
 *   }
 * };
 * var polygons = {
 *   "type": "FeatureCollection",
 *   "features": [poly1, poly2]
 * };
 *
 * var union = turf.union(poly1, poly2);
 *
 * //=polygons
 *
 * //=union
 */
module.exports = function (poly1, poly2) {
    var reader = new jsts.io.GeoJSONReader();
    var a = reader.read(JSON.stringify(poly1.geometry));
    var b = reader.read(JSON.stringify(poly2.geometry));
    var union = a.union(b);
    var writer = new jsts.io.GeoJSONWriter();

    union = writer.write(union);
    return {
        type: 'Feature',
        geometry: union,
        properties: poly1.properties
    };
};

},{"jsts":142}],142:[function(require,module,exports){
arguments[4][20][0].apply(exports,arguments)
},{"dup":20}],143:[function(require,module,exports){
var inside = require('turf-inside');
var featureCollection = require('turf-helpers').featureCollection;

/**
 * Takes a set of {@link Point|points} and a set of {@link Polygon|polygons} and returns the points that fall within the polygons.
 *
 * @name within
 * @param {FeatureCollection<Point>} points input points
 * @param {FeatureCollection<Polygon>} polygons input polygons
 * @return {FeatureCollection<Point>} points that land within at least one polygon
 * @example
 * var searchWithin = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Polygon",
 *         "coordinates": [[
 *           [-46.653,-23.543],
 *           [-46.634,-23.5346],
 *           [-46.613,-23.543],
 *           [-46.614,-23.559],
 *           [-46.631,-23.567],
 *           [-46.653,-23.560],
 *           [-46.653,-23.543]
 *         ]]
 *       }
 *     }
 *   ]
 * };
 * var points = {
 *   "type": "FeatureCollection",
 *   "features": [
 *     {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-46.6318, -23.5523]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-46.6246, -23.5325]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-46.6062, -23.5513]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-46.663, -23.554]
 *       }
 *     }, {
 *       "type": "Feature",
 *       "properties": {},
 *       "geometry": {
 *         "type": "Point",
 *         "coordinates": [-46.643, -23.557]
 *       }
 *     }
 *   ]
 * };
 *
 * var ptsWithin = turf.within(points, searchWithin);
 *
 * //=points
 *
 * //=searchWithin
 *
 * //=ptsWithin
 */
module.exports = function (points, polygons) {
    var pointsWithin = featureCollection([]);
    for (var i = 0; i < polygons.features.length; i++) {
        for (var j = 0; j < points.features.length; j++) {
            var isInside = inside(points.features[j], polygons.features[i]);
            if (isInside) {
                pointsWithin.features.push(points.features[j]);
            }
        }
    }
    return pointsWithin;
};

},{"turf-helpers":144,"turf-inside":145}],144:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],145:[function(require,module,exports){
arguments[4][29][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-inside":75,"dup":29}],146:[function(require,module,exports){
/*eslint global-require: 0*/

/**
 * Turf is a modular geospatial analysis engine written in JavaScript. It performs geospatial
 * processing tasks with GeoJSON data and can be run on a server or in a browser.
 *
 * @module turf
 * @summary Geospatial analysis for JavaScript
 */
module.exports = {
    isolines: require('turf-isolines'),
    convex: require('turf-convex'),
    within: require('turf-within'),
    concave: require('turf-concave'),
    difference: require('turf-difference'),
    collect: require('turf-collect'),
    flip: require('turf-flip'),
    simplify: require('turf-simplify'),
    bezier: require('turf-bezier'),
    tag: require('turf-tag'),
    sample: require('turf-sample'),
    envelope: require('turf-envelope'),
    square: require('turf-square'),
    midpoint: require('turf-midpoint'),
    buffer: require('turf-buffer'),
    center: require('turf-center'),
    centroid: require('turf-centroid'),
    combine: require('turf-combine'),
    distance: require('turf-distance'),
    explode: require('turf-explode'),
    bbox: require('turf-bbox'),
    tesselate: require('turf-tesselate'),
    bboxPolygon: require('turf-bbox-polygon'),
    inside: require('turf-inside'),
    intersect: require('turf-intersect'),
    nearest: require('turf-nearest'),
    planepoint: require('turf-planepoint'),
    random: require('turf-random'),
    tin: require('turf-tin'),
    union: require('turf-union'),
    bearing: require('turf-bearing'),
    destination: require('turf-destination'),
    kinks: require('turf-kinks'),
    pointOnSurface: require('turf-point-on-surface'),
    area: require('turf-area'),
    along: require('turf-along'),
    lineDistance: require('turf-line-distance'),
    lineSlice: require('turf-line-slice'),
    pointOnLine: require('turf-point-on-line'),
    pointGrid: require('turf-point-grid'),
    squareGrid: require('turf-square-grid'),
    triangleGrid: require('turf-triangle-grid'),
    hexGrid: require('turf-hex-grid')
};

var helpers = require('turf-helpers');

module.exports.point = helpers.point;
module.exports.polygon = helpers.polygon;
module.exports.lineString = helpers.lineString;
module.exports.multiPoint = helpers.multiPoint;
module.exports.multiPolygon = helpers.multiPolygon;
module.exports.multiLineString = helpers.multiLineString;
module.exports.feature = helpers.feature;
module.exports.featureCollection = helpers.featureCollection;
module.exports.geometryCollection = helpers.geometryCollection;

},{"turf-along":147,"turf-area":148,"turf-bbox":150,"turf-bbox-polygon":149,"turf-bearing":151,"turf-bezier":152,"turf-buffer":153,"turf-center":154,"turf-centroid":155,"turf-collect":156,"turf-combine":157,"turf-concave":158,"turf-convex":159,"turf-destination":160,"turf-difference":161,"turf-distance":162,"turf-envelope":163,"turf-explode":164,"turf-flip":165,"turf-helpers":166,"turf-hex-grid":167,"turf-inside":168,"turf-intersect":169,"turf-isolines":170,"turf-kinks":171,"turf-line-distance":172,"turf-line-slice":173,"turf-midpoint":174,"turf-nearest":175,"turf-planepoint":176,"turf-point-grid":177,"turf-point-on-line":178,"turf-point-on-surface":179,"turf-random":180,"turf-sample":181,"turf-simplify":182,"turf-square":184,"turf-square-grid":183,"turf-tag":185,"turf-tesselate":186,"turf-tin":187,"turf-triangle-grid":188,"turf-union":189,"turf-within":190}],147:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-along");

},{"/Users/tmcw/src/turf/packages/turf-along":1}],148:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-area");

},{"/Users/tmcw/src/turf/packages/turf-area":6}],149:[function(require,module,exports){
arguments[4][64][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-bbox-polygon":9,"dup":64}],150:[function(require,module,exports){
arguments[4][23][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-bbox":11,"dup":23}],151:[function(require,module,exports){
arguments[4][2][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-bearing":13,"dup":2}],152:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-bezier");

},{"/Users/tmcw/src/turf/packages/turf-bezier":15}],153:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-buffer");

},{"/Users/tmcw/src/turf/packages/turf-buffer":18}],154:[function(require,module,exports){
arguments[4][115][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-center":22,"dup":115}],155:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-centroid");

},{"/Users/tmcw/src/turf/packages/turf-centroid":25}],156:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-collect");

},{"/Users/tmcw/src/turf/packages/turf-collect":28}],157:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-combine");

},{"/Users/tmcw/src/turf/packages/turf-combine":30}],158:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-concave");

},{"/Users/tmcw/src/turf/packages/turf-concave":32}],159:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-convex");

},{"/Users/tmcw/src/turf/packages/turf-convex":36}],160:[function(require,module,exports){
arguments[4][3][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-destination":55,"dup":3}],161:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-difference");

},{"/Users/tmcw/src/turf/packages/turf-difference":58}],162:[function(require,module,exports){
arguments[4][4][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-distance":60,"dup":4}],163:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-envelope");

},{"/Users/tmcw/src/turf/packages/turf-envelope":63}],164:[function(require,module,exports){
arguments[4][117][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-explode":66,"dup":117}],165:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-flip");

},{"/Users/tmcw/src/turf/packages/turf-flip":69}],166:[function(require,module,exports){
arguments[4][5][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-helpers":71,"dup":5}],167:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-hex-grid");

},{"/Users/tmcw/src/turf/packages/turf-hex-grid":72}],168:[function(require,module,exports){
arguments[4][29][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-inside":75,"dup":29}],169:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-intersect");

},{"/Users/tmcw/src/turf/packages/turf-intersect":77}],170:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-isolines");

},{"/Users/tmcw/src/turf/packages/turf-isolines":81}],171:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-kinks");

},{"/Users/tmcw/src/turf/packages/turf-kinks":90}],172:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-line-distance");

},{"/Users/tmcw/src/turf/packages/turf-line-distance":92}],173:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-line-slice");

},{"/Users/tmcw/src/turf/packages/turf-line-slice":95}],174:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-midpoint");

},{"/Users/tmcw/src/turf/packages/turf-midpoint":99}],175:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-nearest");

},{"/Users/tmcw/src/turf/packages/turf-nearest":103}],176:[function(require,module,exports){
arguments[4][86][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-planepoint":105,"dup":86}],177:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-point-grid");

},{"/Users/tmcw/src/turf/packages/turf-point-grid":106}],178:[function(require,module,exports){
arguments[4][97][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-point-on-line":109,"dup":97}],179:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-point-on-surface");

},{"/Users/tmcw/src/turf/packages/turf-point-on-surface":114}],180:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-random");

},{"/Users/tmcw/src/turf/packages/turf-random":120}],181:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-sample");

},{"/Users/tmcw/src/turf/packages/turf-sample":122}],182:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-simplify");

},{"/Users/tmcw/src/turf/packages/turf-simplify":124}],183:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-square-grid");

},{"/Users/tmcw/src/turf/packages/turf-square-grid":126}],184:[function(require,module,exports){
arguments[4][88][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-square":129,"dup":88}],185:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-tag");

},{"/Users/tmcw/src/turf/packages/turf-tag":131}],186:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-tesselate");

},{"/Users/tmcw/src/turf/packages/turf-tesselate":133}],187:[function(require,module,exports){
arguments[4][34][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-tin":136,"dup":34}],188:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-triangle-grid");

},{"/Users/tmcw/src/turf/packages/turf-triangle-grid":138}],189:[function(require,module,exports){
arguments[4][35][0].apply(exports,arguments)
},{"/Users/tmcw/src/turf/packages/turf-union":141,"dup":35}],190:[function(require,module,exports){
module.exports = require("/Users/tmcw/src/turf/packages/turf-within");

},{"/Users/tmcw/src/turf/packages/turf-within":143}]},{},[146])(146)
});

// JSTS. See https://github.com/bjornharrtell/jsts
// Licenses:
// https://github.com/bjornharrtell/jsts/blob/master/LICENSE_EDLv1.txt
// https://github.com/bjornharrtell/jsts/blob/master/LICENSE_EPLv1.txt
// https://github.com/bjornharrtell/jsts/blob/master/LICENSE_LICENSE_ES6_COLLECTIONS.txt
!function(t,e){"object"==typeof exports&&"undefined"!=typeof module?e(exports):"function"==typeof define&&define.amd?define(["exports"],e):e(t.jsts=t.jsts||{})}(this,function(t){"use strict";function e(t,e){for(var n in e)e.hasOwnProperty(n)&&(t[n]=e[n])}function n(){}function i(){}function r(){}function s(){}function o(){}function a(){}function u(){}function l(t){this.name="RuntimeException",this.message=t,this.stack=(new Error).stack,Error.call(this,t)}function h(t,e){t.prototype=Object.create(e.prototype),t.prototype.constructor=t}function c(){if(0===arguments.length)l.call(this);else if(1===arguments.length){var t=arguments[0];l.call(this,t)}}function f(){}function g(){if(this.x=null,this.y=null,this.z=null,0===arguments.length)g.call(this,0,0);else if(1===arguments.length){var t=arguments[0];g.call(this,t.x,t.y,t.z)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];g.call(this,e,n,g.NULL_ORDINATE)}else if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];this.x=i,this.y=r,this.z=s}}function d(){if(this.dimensionsToTest=2,0===arguments.length)d.call(this,2);else if(1===arguments.length){var t=arguments[0];if(2!==t&&3!==t)throw new i("only 2 or 3 dimensions may be specified");this.dimensionsToTest=t}}function p(){}function v(){}function m(t){this.message=t||""}function y(){}function x(t){this.message=t||""}function E(t){this.message=t||""}function I(){this.array_=[],arguments[0]instanceof v&&this.addAll(arguments[0])}function N(){if(I.apply(this),0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.ensureCapacity(t.length),this.add(t,!0)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.ensureCapacity(e.length),this.add(e,n)}}function C(){if(this.minx=null,this.maxx=null,this.miny=null,this.maxy=null,0===arguments.length)this.init();else if(1===arguments.length){if(arguments[0]instanceof g){var t=arguments[0];this.init(t.x,t.x,t.y,t.y)}else if(arguments[0]instanceof C){var e=arguments[0];this.init(e)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.init(n.x,i.x,n.y,i.y)}else if(4===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],a=arguments[3];this.init(r,s,o,a)}}function S(){}function w(){S.call(this,"Projective point not representable on the Cartesian plane.")}function L(){}function R(t,e){return t.interfaces_&&t.interfaces_().indexOf(e)>-1}function T(){}function P(t){this.str=t}function b(t){this.value=t}function O(){}function _(){if(this.hi=0,this.lo=0,0===arguments.length)this.init(0);else if(1===arguments.length){if("number"==typeof arguments[0]){var t=arguments[0];this.init(t)}else if(arguments[0]instanceof _){var e=arguments[0];this.init(e)}else if("string"==typeof arguments[0]){var n=arguments[0];_.call(this,_.parse(n))}}else if(2===arguments.length){var i=arguments[0],r=arguments[1];this.init(i,r)}}function M(){}function D(){}function A(){}function F(){if(this.x=null,this.y=null,this.w=null,0===arguments.length)this.x=0,this.y=0,this.w=1;else if(1===arguments.length){var t=arguments[0];this.x=t.x,this.y=t.y,this.w=1}else if(2===arguments.length){if("number"==typeof arguments[0]&&"number"==typeof arguments[1]){var e=arguments[0],n=arguments[1];this.x=e,this.y=n,this.w=1}else if(arguments[0]instanceof F&&arguments[1]instanceof F){var i=arguments[0],r=arguments[1];this.x=i.y*r.w-r.y*i.w,this.y=r.x*i.w-i.x*r.w,this.w=i.x*r.y-r.x*i.y}else if(arguments[0]instanceof g&&arguments[1]instanceof g){var s=arguments[0],o=arguments[1];this.x=s.y-o.y,this.y=o.x-s.x,this.w=s.x*o.y-o.x*s.y}}else if(3===arguments.length){var a=arguments[0],u=arguments[1],l=arguments[2];this.x=a,this.y=u,this.w=l}else if(4===arguments.length){var h=arguments[0],c=arguments[1],f=arguments[2],d=arguments[3],p=h.y-c.y,v=c.x-h.x,m=h.x*c.y-c.x*h.y,y=f.y-d.y,x=d.x-f.x,E=f.x*d.y-d.x*f.y;this.x=v*E-x*m,this.y=y*m-p*E,this.w=p*x-y*v}}function G(){}function q(){}function B(){this.envelope=null,this.factory=null,this.SRID=null,this.userData=null;var t=arguments[0];this.factory=t,this.SRID=t.getSRID()}function z(){}function V(){}function k(){}function Y(){}function U(){}function X(){}function H(){}function W(){}function j(){}function K(){}function Z(){}function Q(){}function J(){this.array_=[],arguments[0]instanceof v&&this.addAll(arguments[0])}function $(t){return null==t?$s:t.color}function tt(t){return null==t?null:t.parent}function et(t,e){null!==t&&(t.color=e)}function nt(t){return null==t?null:t.left}function it(t){return null==t?null:t.right}function rt(){this.root_=null,this.size_=0}function st(){}function ot(){}function at(){this.array_=[],arguments[0]instanceof v&&this.addAll(arguments[0])}function ut(){}function lt(){}function ht(){}function ct(){}function ft(){this.geometries=null;var t=arguments[0],e=arguments[1];if(B.call(this,e),null===t&&(t=[]),B.hasNullElements(t))throw new i("geometries must not contain null elements");this.geometries=t}function gt(){var t=arguments[0],e=arguments[1];ft.call(this,t,e)}function dt(){if(this.geom=null,this.geomFact=null,this.bnRule=null,this.endpointMap=null,1===arguments.length){var t=arguments[0];dt.call(this,t,V.MOD2_BOUNDARY_RULE)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.geom=e,this.geomFact=e.getFactory(),this.bnRule=n}}function pt(){this.count=null}function vt(){}function mt(){}function yt(){}function xt(){}function Et(){}function It(){}function Nt(){}function Ct(){}function St(){this.points=null;var t=arguments[0],e=arguments[1];B.call(this,e),this.init(t)}function wt(){}function Lt(){this.coordinates=null;var t=arguments[0],e=arguments[1];B.call(this,e),this.init(t)}function Rt(){}function Tt(){this.shell=null,this.holes=null;var t=arguments[0],e=arguments[1],n=arguments[2];if(B.call(this,n),null===t&&(t=this.getFactory().createLinearRing()),null===e&&(e=[]),B.hasNullElements(e))throw new i("holes must not contain null elements");if(t.isEmpty()&&B.hasNonEmptyElements(e))throw new i("shell is empty but holes are not");this.shell=t,this.holes=e}function Pt(){var t=arguments[0],e=arguments[1];ft.call(this,t,e)}function bt(){if(arguments[0]instanceof g&&arguments[1]instanceof ie){var t=arguments[0],e=arguments[1];bt.call(this,e.getCoordinateSequenceFactory().create(t),e)}else if(R(arguments[0],D)&&arguments[1]instanceof ie){var n=arguments[0],i=arguments[1];St.call(this,n,i),this.validateConstruction()}}function Ot(){var t=arguments[0],e=arguments[1];ft.call(this,t,e)}function _t(){if(this.factory=null,this.isUserDataCopied=!1,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.factory=t}}function Mt(){}function Dt(){}function At(){}function Ft(){}function Gt(){if(this.dimension=3,this.coordinates=null,1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];Gt.call(this,t,3)}else if(Number.isInteger(arguments[0])){var e=arguments[0];this.coordinates=new Array(e).fill(null);for(var n=0;e>n;n++)this.coordinates[n]=new g}else if(R(arguments[0],D)){var i=arguments[0];if(null===i)return this.coordinates=new Array(0).fill(null),null;this.dimension=i.getDimension(),this.coordinates=new Array(i.size()).fill(null);for(var n=0;n<this.coordinates.length;n++)this.coordinates[n]=i.getCoordinateCopy(n)}}else if(2===arguments.length)if(arguments[0]instanceof Array&&Number.isInteger(arguments[1])){var r=arguments[0],s=arguments[1];this.coordinates=r,this.dimension=s,null===r&&(this.coordinates=new Array(0).fill(null))}else if(Number.isInteger(arguments[0])&&Number.isInteger(arguments[1])){var o=arguments[0],a=arguments[1];this.coordinates=new Array(o).fill(null),this.dimension=a;for(var n=0;o>n;n++)this.coordinates[n]=new g}}function qt(){}function Bt(t,e){return t===e||t!==t&&e!==e}function zt(t,e){function n(t){return this&&this.constructor===n?(this._keys=[],this._values=[],this._itp=[],this.objectOnly=e,void(t&&Vt.call(this,t))):new n(t)}return e||io(t,"size",{get:Jt}),t.constructor=n,n.prototype=t,n}function Vt(t){this.add?t.forEach(this.add,this):t.forEach(function(t){this.set(t[0],t[1])},this)}function kt(t){return this.has(t)&&(this._keys.splice(no,1),this._values.splice(no,1),this._itp.forEach(function(t){no<t[0]&&t[0]--})),no>-1}function Yt(t){return this.has(t)?this._values[no]:void 0}function Ut(t,e){if(this.objectOnly&&e!==Object(e))throw new TypeError("Invalid value used as weak collection key");if(e!==e||0===e)for(no=t.length;no--&&!Bt(t[no],e););else no=t.indexOf(e);return no>-1}function Xt(t){return Ut.call(this,this._keys,t)}function Ht(t,e){return this.has(t)?this._values[no]=e:this._values[this._keys.push(t)-1]=e,this}function Wt(){(this._keys||0).length=this._values.length=0}function jt(){return Qt(this._itp,this._keys)}function Kt(){return Qt(this._itp,this._values)}function Zt(){return Qt(this._itp,this._keys,this._values)}function Qt(t,e,n){var i=[0],r=!1;return t.push(i),{next:function(){var s,o=i[0];return!r&&o<e.length?(s=n?[e[o],n[o]]:e[o],i[0]++):(r=!0,t.splice(t.indexOf(i),1)),{done:r,value:s}}}}function Jt(){return this._values.length}function $t(t,e){for(var n=this.entries();;){var i=n.next();if(i.done)break;t.call(e,i.value[1],i.value[0],this)}}function te(){this.map_=new so}function ee(){if(this.modelType=null,this.scale=null,0===arguments.length)this.modelType=ee.FLOATING;else if(1===arguments.length)if(arguments[0]instanceof ne){var t=arguments[0];this.modelType=t,t===ee.FIXED&&this.setScale(1)}else if("number"==typeof arguments[0]){var e=arguments[0];this.modelType=ee.FIXED,this.setScale(e)}else if(arguments[0]instanceof ee){var n=arguments[0];this.modelType=n.modelType,this.scale=n.scale}}function ne(){this.name=null;var t=arguments[0];this.name=t,ne.nameToTypeMap.put(t,this)}function ie(){if(this.precisionModel=null,this.coordinateSequenceFactory=null,this.SRID=null,0===arguments.length)ie.call(this,new ee,0);else if(1===arguments.length){if(R(arguments[0],G)){var t=arguments[0];ie.call(this,new ee,0,t)}else if(arguments[0]instanceof ee){var e=arguments[0];ie.call(this,e,0,ie.getDefaultCoordinateSequenceFactory())}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];ie.call(this,n,i,ie.getDefaultCoordinateSequenceFactory())}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];this.precisionModel=r,this.coordinateSequenceFactory=o,this.SRID=s}}function re(t){this.geometryFactory=t||new ie}function se(t){this.parser=new re(t)}function oe(){this.result=null,this.inputLines=Array(2).fill().map(function(){return Array(2)}),this.intPt=new Array(2).fill(null),this.intLineIndex=null,this._isProper=null,this.pa=null,this.pb=null,this.precisionModel=null,this.intPt[0]=new g,this.intPt[1]=new g,this.pa=this.intPt[0],this.pb=this.intPt[1],this.result=0}function ae(){oe.apply(this)}function ue(){}function le(){this.p=null,this.crossingCount=0,this.isPointOnSegment=!1;var t=arguments[0];this.p=t}function he(){}function ce(){if(this.p0=null,this.p1=null,0===arguments.length)ce.call(this,new g,new g);else if(1===arguments.length){var t=arguments[0];ce.call(this,t.p0,t.p1)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.p0=e,this.p1=n}else if(4===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2],o=arguments[3];ce.call(this,new g(i,r),new g(s,o))}}function fe(){if(this.matrix=null,0===arguments.length)this.matrix=Array(3).fill().map(function(){return Array(3)}),this.setAll(lt.FALSE);else if(1===arguments.length)if("string"==typeof arguments[0]){var t=arguments[0];fe.call(this),this.set(t)}else if(arguments[0]instanceof fe){var e=arguments[0];fe.call(this),this.matrix[L.INTERIOR][L.INTERIOR]=e.matrix[L.INTERIOR][L.INTERIOR],this.matrix[L.INTERIOR][L.BOUNDARY]=e.matrix[L.INTERIOR][L.BOUNDARY],this.matrix[L.INTERIOR][L.EXTERIOR]=e.matrix[L.INTERIOR][L.EXTERIOR],this.matrix[L.BOUNDARY][L.INTERIOR]=e.matrix[L.BOUNDARY][L.INTERIOR],this.matrix[L.BOUNDARY][L.BOUNDARY]=e.matrix[L.BOUNDARY][L.BOUNDARY],this.matrix[L.BOUNDARY][L.EXTERIOR]=e.matrix[L.BOUNDARY][L.EXTERIOR],this.matrix[L.EXTERIOR][L.INTERIOR]=e.matrix[L.EXTERIOR][L.INTERIOR],this.matrix[L.EXTERIOR][L.BOUNDARY]=e.matrix[L.EXTERIOR][L.BOUNDARY],this.matrix[L.EXTERIOR][L.EXTERIOR]=e.matrix[L.EXTERIOR][L.EXTERIOR]}}function ge(){this.areaBasePt=null,this.triangleCent3=new g,this.areasum2=0,this.cg3=new g,this.lineCentSum=new g,this.totalLength=0,this.ptCount=0,this.ptCentSum=new g;var t=arguments[0];this.areaBasePt=null,this.add(t)}function de(t){this.message=t||""}function pe(){this.array_=[]}function ve(){this.treeSet=new at,this.list=new I}function me(){if(this.geomFactory=null,this.inputPts=null,1===arguments.length){var t=arguments[0];me.call(this,me.extractCoordinates(t),t.getFactory())}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.inputPts=ve.filterCoordinates(e),this.geomFactory=n}}function ye(){this.origin=null;var t=arguments[0];this.origin=t}function xe(){this.inputGeom=null,this.factory=null,this.pruneEmptyGeometry=!0,this.preserveGeometryCollectionType=!0,this.preserveCollections=!1,this.preserveType=!1}function Ee(){if(this.snapTolerance=0,this.srcPts=null,this.seg=new ce,this.allowSnappingToSourceVertices=!1,this._isClosed=!1,arguments[0]instanceof St&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1];Ee.call(this,t.getCoordinates(),e)}else if(arguments[0]instanceof Array&&"number"==typeof arguments[1]){var n=arguments[0],i=arguments[1];this.srcPts=n,this._isClosed=Ee.isClosed(n),this.snapTolerance=i}}function Ie(){this.srcGeom=null;var t=arguments[0];this.srcGeom=t}function Ne(){if(xe.apply(this),this.snapTolerance=null,this.snapPts=null,this.isSelfSnap=!1,2===arguments.length){var t=arguments[0],e=arguments[1];this.snapTolerance=t,this.snapPts=e}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.snapTolerance=n,this.snapPts=i,this.isSelfSnap=r}}function Ce(){this.isFirst=!0,this.commonMantissaBitsCount=53,this.commonBits=0,this.commonSignExp=null}function Se(){this.commonCoord=null,this.ccFilter=new we}function we(){this.commonBitsX=new Ce,this.commonBitsY=new Ce}function Le(){this.trans=null;var t=arguments[0];this.trans=t}function Re(){this.parent=null,this.atStart=null,this.max=null,this.index=null,this.subcollectionIterator=null;var t=arguments[0];this.parent=t,this.atStart=!0,this.index=0,this.max=t.getNumGeometries()}function Te(){if(this.boundaryRule=V.OGC_SFS_BOUNDARY_RULE,this.isIn=null,this.numBoundaries=null,0===arguments.length);else if(1===arguments.length){var t=arguments[0];if(null===t)throw new i("Rule must be non-null");this.boundaryRule=t}}function Pe(){}function be(){}function Oe(){this.pts=null,this.data=null;var t=arguments[0],e=arguments[1];this.pts=t,this.data=e}function _e(){}function Me(){this.bounds=null,this.item=null;var t=arguments[0],e=arguments[1];this.bounds=t,this.item=e}function De(){this._size=null,this.items=null,this._size=0,this.items=new I,this.items.add(null)}function Ae(){}function Fe(){}function Ge(){if(this.childBoundables=new I,this.bounds=null,this.level=null,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.level=t}}function qe(){this.boundable1=null,this.boundable2=null,this._distance=null,this.itemDistance=null;var t=arguments[0],e=arguments[1],n=arguments[2];this.boundable1=t,this.boundable2=e,this.itemDistance=n,this._distance=this.distance()}function Be(){if(this.root=null,this.built=!1,this.itemBoundables=new I,this.nodeCapacity=null,0===arguments.length)Be.call(this,Be.DEFAULT_NODE_CAPACITY);else if(1===arguments.length){var t=arguments[0];f.isTrue(t>1,"Node capacity must be greater than 1"),this.nodeCapacity=t}}function ze(){}function Ve(){}function ke(){if(0===arguments.length)ke.call(this,ke.DEFAULT_NODE_CAPACITY);else if(1===arguments.length){var t=arguments[0];Be.call(this,t)}}function Ye(){var t=arguments[0];Ge.call(this,t)}function Ue(){}function Xe(){this.segString=null,this.coord=null,this.segmentIndex=null,this.segmentOctant=null,this._isInterior=null;var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];this.segString=t,this.coord=new g(e),this.segmentIndex=n,this.segmentOctant=i,this._isInterior=!e.equals2D(t.getCoordinate(n))}function He(){this.nodeMap=new rt,this.edge=null;var t=arguments[0];this.edge=t}function We(){this.nodeList=null,this.edge=null,this.nodeIt=null,this.currNode=null,this.nextNode=null,this.currSegIndex=0;var t=arguments[0];this.nodeList=t,this.edge=t.getEdge(),this.nodeIt=t.iterator(),this.readNextNode()}function je(){}function Ke(){this.nodeList=new He(this),this.pts=null,this.data=null;var t=arguments[0],e=arguments[1];this.pts=t,this.data=e}function Ze(){this.tempEnv1=new C,this.tempEnv2=new C,this.overlapSeg1=new ce,this.overlapSeg2=new ce}function Qe(){this.pts=null,this.start=null,this.end=null,this.env=null,this.context=null,this.id=null;var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];this.pts=t,this.start=e,this.end=n,this.context=i}function Je(){}function $e(){}function tn(){}function en(){if(this.segInt=null,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.setSegmentIntersector(t)}}function nn(){if(this.monoChains=new I,this.index=new ke,this.idCounter=0,this.nodedSegStrings=null,this.nOverlaps=0,0===arguments.length);else if(1===arguments.length){var t=arguments[0];en.call(this,t)}}function rn(){Ze.apply(this),this.si=null;var t=arguments[0];this.si=t}function sn(){if(this.pt=null,1===arguments.length){var t=arguments[0];l.call(this,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];l.call(this,sn.msgWithCoord(e,n)),this.name="TopologyException",this.pt=new g(n)}}function on(){}function an(){this.findAllIntersections=!1,this.isCheckEndSegmentsOnly=!1,this.li=null,this.interiorIntersection=null,this.intSegments=null,this.intersections=new I,this.intersectionCount=0,this.keepIntersections=!0;var t=arguments[0];this.li=t,this.interiorIntersection=null}function un(){this.li=new ae,this.segStrings=null,this.findAllIntersections=!1,this.segInt=null,this._isValid=!0;var t=arguments[0];this.segStrings=t}function ln(){this.nv=null;var t=arguments[0];this.nv=new un(ln.toSegmentStrings(t))}function hn(){this.mapOp=null;var t=arguments[0];this.mapOp=t}function cn(){}function fn(){if(this.location=null,1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];this.init(t.length)}else if(Number.isInteger(arguments[0])){var e=arguments[0];this.init(1),this.location[cn.ON]=e}else if(arguments[0]instanceof fn){var n=arguments[0];if(this.init(n.location.length),null!==n)for(var i=0;i<this.location.length;i++)this.location[i]=n.location[i]}}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];this.init(3),this.location[cn.ON]=r,this.location[cn.LEFT]=s,this.location[cn.RIGHT]=o}}function gn(){if(this.elt=new Array(2).fill(null),1===arguments.length){if(Number.isInteger(arguments[0])){var t=arguments[0];this.elt[0]=new fn(t),this.elt[1]=new fn(t)}else if(arguments[0]instanceof gn){var e=arguments[0];this.elt[0]=new fn(e.elt[0]),this.elt[1]=new fn(e.elt[1])}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.elt[0]=new fn(L.NONE),this.elt[1]=new fn(L.NONE),this.elt[n].setLocation(i)}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];this.elt[0]=new fn(r,s,o),this.elt[1]=new fn(r,s,o)}else if(4===arguments.length){var a=arguments[0],u=arguments[1],l=arguments[2],h=arguments[3];this.elt[0]=new fn(L.NONE,L.NONE,L.NONE),this.elt[1]=new fn(L.NONE,L.NONE,L.NONE),this.elt[a].setLocations(u,l,h)}}function dn(){this.startDe=null,this.maxNodeDegree=-1,this.edges=new I,this.pts=new I,this.label=new gn(L.NONE),this.ring=null,this._isHole=null,this.shell=null,this.holes=new I,this.geometryFactory=null;var t=arguments[0],e=arguments[1];this.geometryFactory=e,this.computePoints(t),this.computeRing()}function pn(){var t=arguments[0],e=arguments[1];dn.call(this,t,e)}function vn(){var t=arguments[0],e=arguments[1];dn.call(this,t,e)}function mn(){if(this.label=null,this._isInResult=!1,this._isCovered=!1,this._isCoveredSet=!1,this._isVisited=!1,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.label=t}}function yn(){mn.apply(this),this.coord=null,this.edges=null;var t=arguments[0],e=arguments[1];this.coord=t,this.edges=e,this.label=new gn(0,L.NONE)}function xn(){this.nodeMap=new rt,this.nodeFact=null;var t=arguments[0];this.nodeFact=t}function En(){if(this.edge=null,this.label=null,this.node=null,this.p0=null,this.p1=null,this.dx=null,this.dy=null,this.quadrant=null,1===arguments.length){var t=arguments[0];this.edge=t}else if(3===arguments.length){var e=arguments[0],n=arguments[1],i=arguments[2];En.call(this,e,n,i,null)}else if(4===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],a=arguments[3];En.call(this,r),this.init(s,o),this.label=a}}function In(){this._isForward=null,this._isInResult=!1,this._isVisited=!1,this.sym=null,this.next=null,this.nextMin=null,this.edgeRing=null,this.minEdgeRing=null,this.depth=[0,-999,-999];var t=arguments[0],e=arguments[1];if(En.call(this,t),this._isForward=e,e)this.init(t.getCoordinate(0),t.getCoordinate(1));else{var n=t.getNumPoints()-1;this.init(t.getCoordinate(n),t.getCoordinate(n-1))}this.computeDirectedLabel()}function Nn(){}function Cn(){if(this.edges=new I,this.nodes=null,this.edgeEndList=new I,0===arguments.length)this.nodes=new xn(new Nn);else if(1===arguments.length){var t=arguments[0];this.nodes=new xn(t)}}function Sn(){this.geometryFactory=null,this.shellList=new I;var t=arguments[0];this.geometryFactory=t}function wn(){this.op=null,this.geometryFactory=null,this.ptLocator=null,this.lineEdgesList=new I,this.resultLineList=new I;var t=arguments[0],e=arguments[1],n=arguments[2];this.op=t,this.geometryFactory=e,this.ptLocator=n}function Ln(){this.op=null,this.geometryFactory=null,this.resultPointList=new I;var t=arguments[0],e=arguments[1];arguments[2];this.op=t,this.geometryFactory=e}function Rn(){}function Tn(){this.geom=null;var t=arguments[0];this.geom=t}function Pn(){this.edgeMap=new rt,this.edgeList=null,this.ptInAreaLocation=[L.NONE,L.NONE]}function bn(){Pn.apply(this),this.resultAreaEdgeList=null,this.label=null,this.SCANNING_FOR_INCOMING=1,this.LINKING_TO_OUTGOING=2}function On(){Nn.apply(this)}function _n(){this.mce=null,this.chainIndex=null;var t=arguments[0],e=arguments[1];this.mce=t,this.chainIndex=e}function Mn(){if(this.label=null,this.xValue=null,this.eventType=null,this.insertEvent=null,this.deleteEventIndex=null,this.obj=null,2===arguments.length){var t=arguments[0],e=arguments[1];this.eventType=Mn.DELETE,this.xValue=t,this.insertEvent=e}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.eventType=Mn.INSERT,this.label=n,this.xValue=i,this.obj=r}}function Dn(){}function An(){this._hasIntersection=!1,this.hasProper=!1,this.hasProperInterior=!1,this.properIntersectionPoint=null,this.li=null,this.includeProper=null,this.recordIsolated=null,this.isSelfIntersection=null,this.numIntersections=0,this.numTests=0,this.bdyNodes=null,this._isDone=!1,this.isDoneWhenProperInt=!1;var t=arguments[0],e=arguments[1],n=arguments[2];this.li=t,this.includeProper=e,this.recordIsolated=n}function Fn(){Dn.apply(this),this.events=new I,this.nOverlaps=null}function Gn(){this.min=r.POSITIVE_INFINITY,this.max=r.NEGATIVE_INFINITY}function qn(){}function Bn(){Gn.apply(this),this.item=null;var t=arguments[0],e=arguments[1],n=arguments[2];this.min=t,this.max=e,this.item=n}function zn(){Gn.apply(this),this.node1=null,this.node2=null;var t=arguments[0],e=arguments[1];this.node1=t,this.node2=e,this.buildExtent(this.node1,this.node2)}function Vn(){this.leaves=new I,this.root=null,this.level=0}function kn(){if(this.lines=null,this.isForcedToLineString=!1,1===arguments.length){var t=arguments[0];this.lines=t}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.lines=e,this.isForcedToLineString=n}}function Yn(){this.items=new I}function Un(){this.index=null;var t=arguments[0];if(!R(t,Rt))throw new i("Argument must be Polygonal");this.index=new Hn(t)}function Xn(){this.counter=null;var t=arguments[0];this.counter=t}function Hn(){this.index=new Vn;var t=arguments[0];this.init(t)}function Wn(){this.coord=null,this.segmentIndex=null,this.dist=null;var t=arguments[0],e=arguments[1],n=arguments[2];this.coord=new g(t),this.segmentIndex=e,this.dist=n}function jn(){this.nodeMap=new rt,this.edge=null;var t=arguments[0];this.edge=t}function Kn(){}function Zn(){this.e=null,this.pts=null,this.startIndex=null,this.env1=new C,this.env2=new C;var t=arguments[0];this.e=t,this.pts=t.getCoordinates();var e=new Kn;this.startIndex=e.getChainStartIndices(this.pts)}function Qn(){this.depth=Array(2).fill().map(function(){return Array(3)});for(var t=0;2>t;t++)for(var e=0;3>e;e++)this.depth[t][e]=Qn.NULL_VALUE}function Jn(){if(mn.apply(this),this.pts=null,this.env=null,this.eiList=new jn(this),this.name=null,this.mce=null,this._isIsolated=!0,this.depth=new Qn,this.depthDelta=0,1===arguments.length){var t=arguments[0];Jn.call(this,t,null)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.pts=e,this.label=n}}function $n(){if(Cn.apply(this),this.parentGeom=null,this.lineEdgeMap=new te,this.boundaryNodeRule=null,this.useBoundaryDeterminationRule=!0,this.argIndex=null,this.boundaryNodes=null,this._hasTooFewPoints=!1,this.invalidPoint=null,this.areaPtLocator=null,this.ptLocator=new Te,2===arguments.length){var t=arguments[0],e=arguments[1];$n.call(this,t,e,V.OGC_SFS_BOUNDARY_RULE)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.argIndex=n,this.parentGeom=i,this.boundaryNodeRule=r,null!==i&&this.add(i)}}function ti(){if(this.li=new ae,this.resultPrecisionModel=null,this.arg=null,1===arguments.length){var t=arguments[0];this.setComputationPrecision(t.getPrecisionModel()),this.arg=new Array(1).fill(null),this.arg[0]=new $n(0,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];ti.call(this,e,n,V.OGC_SFS_BOUNDARY_RULE)}else if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];i.getPrecisionModel().compareTo(r.getPrecisionModel())>=0?this.setComputationPrecision(i.getPrecisionModel()):this.setComputationPrecision(r.getPrecisionModel()),this.arg=new Array(2).fill(null),this.arg[0]=new $n(0,i,s),this.arg[1]=new $n(1,r,s)}}function ei(){this.pts=null,this._orientation=null;var t=arguments[0];this.pts=t,this._orientation=ei.orientation(t)}function ni(){this.edges=new I,this.ocaMap=new rt}function ii(){this.ptLocator=new Te,this.geomFact=null,this.resultGeom=null,this.graph=null,this.edgeList=new ni,this.resultPolyList=new I,this.resultLineList=new I,this.resultPointList=new I;var t=arguments[0],e=arguments[1];ti.call(this,t,e),this.graph=new Cn(new On),this.geomFact=t.getFactory()}function ri(){this.geom=new Array(2).fill(null),this.snapTolerance=null,this.cbr=null;var t=arguments[0],e=arguments[1];this.geom[0]=t,this.geom[1]=e,this.computeSnapTolerance()}function si(){this.geom=new Array(2).fill(null);var t=arguments[0],e=arguments[1];this.geom[0]=t,this.geom[1]=e}function oi(){this.factory=null,this.interiorPoint=null,this.maxWidth=0;var t=arguments[0];this.factory=t.getFactory(),this.add(t)}function ai(){this.poly=null,this.centreY=null,this.hiY=r.MAX_VALUE,this.loY=-r.MAX_VALUE;var t=arguments[0];this.poly=t,this.hiY=t.getEnvelopeInternal().getMaxY(),this.loY=t.getEnvelopeInternal().getMinY(),this.centreY=oi.avg(this.loY,this.hiY)}function ui(){this.centroid=null,this.minDistance=r.MAX_VALUE,this.interiorPoint=null;var t=arguments[0];this.centroid=t.getCentroid().getCoordinate(),this.addInterior(t),null===this.interiorPoint&&this.addEndpoints(t)}function li(){this.centroid=null,this.minDistance=r.MAX_VALUE,this.interiorPoint=null;var t=arguments[0];this.centroid=t.getCentroid().getCoordinate(),this.add(t)}function hi(){this.tempEnv1=new C,this.selectedSegment=new ce}function ci(){this.items=new I,this.subnode=[null,null]}function fi(){if(this.min=null,this.max=null,0===arguments.length)this.min=0,this.max=0;else if(1===arguments.length){var t=arguments[0];this.init(t.min,t.max)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.init(e,n)}}function gi(){}function di(t,e){var n,i,r,s,o={32:{d:127,c:128,b:0,a:0},64:{d:32752,c:0,b:0,a:0}},a={32:8,64:11}[t];if(s||(n=0>e||0>1/e,isFinite(e)||(s=o[t],n&&(s.d+=1<<t/4-1),i=Math.pow(2,a)-1,r=0)),!s){for(i={32:127,64:1023}[t],r=Math.abs(e);r>=2;)i++,r/=2;for(;1>r&&i>0;)i--,r*=2;0>=i&&(r/=2),32===t&&i>254&&(s={d:n?255:127,c:128,b:0,a:0},i=Math.pow(2,a)-1,r=0)}return i}function pi(){this.pt=0,this.level=0,this.interval=null;var t=arguments[0];this.computeKey(t)}function vi(){ci.apply(this),this.interval=null,this.centre=null,this.level=null;var t=arguments[0],e=arguments[1];this.interval=t,this.level=e,this.centre=(t.getMin()+t.getMax())/2}function mi(){}function yi(){ci.apply(this)}function xi(){this.root=null,this.minExtent=1,this.root=new yi}function Ei(){}function Ii(){this.ring=null,this.tree=null,this.crossings=0,this.interval=new fi;var t=arguments[0];this.ring=t,this.buildIndex()}function Ni(){hi.apply(this),this.mcp=null,this.p=null;var t=arguments[0],e=arguments[1];this.mcp=t,this.p=e}function Ci(){}function Si(){this.p0=null,this.p1=null,this.p2=null;var t=arguments[0],e=arguments[1],n=arguments[2];this.p0=t,this.p1=e,this.p2=n}function wi(){this.input=null,this.extremalPts=null,this.centre=null,this.radius=0;var t=arguments[0];this.input=t}function Li(){if(this.inputGeom=null,this.isConvex=null,this.convexHullPts=null,this.minBaseSeg=new ce,this.minWidthPt=null,this.minPtIndex=null,this.minWidth=0,1===arguments.length){var t=arguments[0];Li.call(this,t,!1)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.inputGeom=e,this.isConvex=n}}function Ri(){this.inputGeom=null,this.distanceTolerance=null;var t=arguments[0];this.inputGeom=t}function Ti(){xe.apply(this),this.distanceTolerance=null;var t=arguments[0];this.distanceTolerance=t}function Pi(){this._orig=null,this._sym=null,this._next=null;var t=arguments[0];this._orig=t}function bi(){this._isMarked=!1;var t=arguments[0];Pi.call(this,t)}function Oi(){this.vertexMap=new te}function _i(){this._isStart=!1;var t=arguments[0];bi.call(this,t)}function Mi(){Oi.apply(this)}function Di(){this.result=null,this.factory=null,this.graph=null,this.lines=new I,this.nodeEdgeStack=new pe,this.ringStartEdge=null,this.graph=new Mi}function Ai(){this.items=new I,this.subnode=new Array(4).fill(null)}function Fi(){this.pt=new g,this.level=0,this.env=null;var t=arguments[0];this.computeKey(t)}function Gi(){Ai.apply(this),this.env=null,this.centrex=null,this.centrey=null,this.level=null;var t=arguments[0],e=arguments[1];this.env=t,this.level=e,this.centrex=(t.getMinX()+t.getMaxX())/2,this.centrey=(t.getMinY()+t.getMaxY())/2}function qi(){Ai.apply(this)}function Bi(){this.root=null,this.minExtent=1,this.root=new qi}function zi(t){this.geometryFactory=t||new ie}function Vi(t){this.geometryFactory=t||new ie,this.precisionModel=this.geometryFactory.getPrecisionModel(),this.parser=new zi(this.geometryFactory)}function ki(){this.parser=new zi(this.geometryFactory)}function Yi(t){this.geometryFactory=t||new ie,this.precisionModel=this.geometryFactory.getPrecisionModel(),this.parser=new re(this.geometryFactory)}function Ui(t){return[t.x,t.y]}function Xi(t){this.geometryFactory=t||new ie}function Hi(){if(this.noder=null,this.scaleFactor=null,this.offsetX=null,this.offsetY=null,this.isScaled=!1,2===arguments.length){var t=arguments[0],e=arguments[1];Hi.call(this,t,e,0,0)}else if(4===arguments.length){var n=arguments[0],i=arguments[1];arguments[2],arguments[3];this.noder=n,this.scaleFactor=i,this.isScaled=!this.isIntegerPrecision()}}function Wi(){if(this.inputGeom=null,this.isClosedEndpointsInInterior=!0,this.nonSimpleLocation=null,1===arguments.length){var t=arguments[0];this.inputGeom=t}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.inputGeom=e,
this.isClosedEndpointsInInterior=!n.isInBoundary(2)}}function ji(){this.pt=null,this.isClosed=null,this.degree=null;var t=arguments[0];this.pt=t,this.isClosed=!1,this.degree=0}function Ki(){if(this.quadrantSegments=Ki.DEFAULT_QUADRANT_SEGMENTS,this.endCapStyle=Ki.CAP_ROUND,this.joinStyle=Ki.JOIN_ROUND,this.mitreLimit=Ki.DEFAULT_MITRE_LIMIT,this._isSingleSided=!1,this.simplifyFactor=Ki.DEFAULT_SIMPLIFY_FACTOR,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.setQuadrantSegments(t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.setQuadrantSegments(e),this.setEndCapStyle(n)}else if(4===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2],o=arguments[3];this.setQuadrantSegments(i),this.setEndCapStyle(r),this.setJoinStyle(s),this.setMitreLimit(o)}}function Zi(){this.minIndex=-1,this.minCoord=null,this.minDe=null,this.orientedDe=null}function Qi(){this.array_=[]}function Ji(){this.finder=null,this.dirEdgeList=new I,this.nodes=new I,this.rightMostCoord=null,this.env=null,this.finder=new Zi}function $i(){this.inputLine=null,this.distanceTol=null,this.isDeleted=null,this.angleOrientation=he.COUNTERCLOCKWISE;var t=arguments[0];this.inputLine=t}function tr(){this.ptList=null,this.precisionModel=null,this.minimimVertexDistance=0,this.ptList=new I}function er(){this.maxCurveSegmentError=0,this.filletAngleQuantum=null,this.closingSegLengthFactor=1,this.segList=null,this.distance=0,this.precisionModel=null,this.bufParams=null,this.li=null,this.s0=null,this.s1=null,this.s2=null,this.seg0=new ce,this.seg1=new ce,this.offset0=new ce,this.offset1=new ce,this.side=0,this._hasNarrowConcaveAngle=!1;var t=arguments[0],e=arguments[1],n=arguments[2];this.precisionModel=t,this.bufParams=e,this.li=new ae,this.filletAngleQuantum=Math.PI/2/e.getQuadrantSegments(),e.getQuadrantSegments()>=8&&e.getJoinStyle()===Ki.JOIN_ROUND&&(this.closingSegLengthFactor=er.MAX_CLOSING_SEG_LEN_FACTOR),this.init(n)}function nr(){this.distance=0,this.precisionModel=null,this.bufParams=null;var t=arguments[0],e=arguments[1];this.precisionModel=t,this.bufParams=e}function ir(){this.subgraphs=null,this.seg=new ce,this.cga=new he;var t=arguments[0];this.subgraphs=t}function rr(){this.upwardSeg=null,this.leftDepth=null;var t=arguments[0],e=arguments[1];this.upwardSeg=new ce(t),this.leftDepth=e}function sr(){this.inputGeom=null,this.distance=null,this.curveBuilder=null,this.curveList=new I;var t=arguments[0],e=arguments[1],n=arguments[2];this.inputGeom=t,this.distance=e,this.curveBuilder=n}function or(){this._hasIntersection=!1,this.hasProper=!1,this.hasProperInterior=!1,this.hasInterior=!1,this.properIntersectionPoint=null,this.li=null,this.isSelfIntersection=null,this.numIntersections=0,this.numInteriorIntersections=0,this.numProperIntersections=0,this.numTests=0;var t=arguments[0];this.li=t}function ar(){this.bufParams=null,this.workingPrecisionModel=null,this.workingNoder=null,this.geomFact=null,this.graph=null,this.edgeList=new ni;var t=arguments[0];this.bufParams=t}function ur(){this.li=new ae,this.segStrings=null;var t=arguments[0];this.segStrings=t}function lr(){this.li=null,this.pt=null,this.originalPt=null,this.ptScaled=null,this.p0Scaled=null,this.p1Scaled=null,this.scaleFactor=null,this.minx=null,this.maxx=null,this.miny=null,this.maxy=null,this.corner=new Array(4).fill(null),this.safeEnv=null;var t=arguments[0],e=arguments[1],n=arguments[2];if(this.originalPt=t,this.pt=t,this.scaleFactor=e,this.li=n,0>=e)throw new i("Scale factor must be non-zero");1!==e&&(this.pt=new g(this.scale(t.x),this.scale(t.y)),this.p0Scaled=new g,this.p1Scaled=new g),this.initCorners(this.pt)}function hr(){this.index=null;var t=arguments[0];this.index=t}function cr(){hi.apply(this),this.hotPixel=null,this.parentEdge=null,this.hotPixelVertexIndex=null,this._isNodeAdded=!1;var t=arguments[0],e=arguments[1],n=arguments[2];this.hotPixel=t,this.parentEdge=e,this.hotPixelVertexIndex=n}function fr(){this.li=null,this.interiorIntersections=null;var t=arguments[0];this.li=t,this.interiorIntersections=new I}function gr(){this.pm=null,this.li=null,this.scaleFactor=null,this.noder=null,this.pointSnapper=null,this.nodedSegStrings=null;var t=arguments[0];this.pm=t,this.li=new ae,this.li.setPrecisionModel(t),this.scaleFactor=t.getScale()}function dr(){if(this.argGeom=null,this.distance=null,this.bufParams=new Ki,this.resultGeometry=null,this.saveException=null,1===arguments.length){var t=arguments[0];this.argGeom=t}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.argGeom=e,this.bufParams=n}}function pr(){this.comps=null;var t=arguments[0];this.comps=t}function vr(){if(this.component=null,this.segIndex=null,this.pt=null,2===arguments.length){var t=arguments[0],e=arguments[1];vr.call(this,t,vr.INSIDE_AREA,e)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.component=n,this.segIndex=i,this.pt=r}}function mr(){this.pts=null;var t=arguments[0];this.pts=t}function yr(){this.locations=null;var t=arguments[0];this.locations=t}function xr(){if(this.geom=null,this.terminateDistance=0,this.ptLocator=new Te,this.minDistanceLocation=null,this.minDistance=r.MAX_VALUE,2===arguments.length){var t=arguments[0],e=arguments[1];xr.call(this,t,e,0)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],s=arguments[2];this.geom=new Array(2).fill(null),this.geom[0]=n,this.geom[1]=i,this.terminateDistance=s}}function Er(){this.factory=null,this.directedEdges=new I,this.coordinates=null;var t=arguments[0];this.factory=t}function Ir(){this._isMarked=!1,this._isVisited=!1,this.data=null}function Nr(){Ir.apply(this),this.parentEdge=null,this.from=null,this.to=null,this.p0=null,this.p1=null,this.sym=null,this.edgeDirection=null,this.quadrant=null,this.angle=null;var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];this.from=t,this.to=e,this.edgeDirection=i,this.p0=t.getCoordinate(),this.p1=n;var r=this.p1.x-this.p0.x,s=this.p1.y-this.p0.y;this.quadrant=Je.quadrant(r,s),this.angle=Math.atan2(s,r)}function Cr(){var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];Nr.call(this,t,e,n,i)}function Sr(){if(Ir.apply(this),this.dirEdge=null,0===arguments.length);else if(2===arguments.length){var t=arguments[0],e=arguments[1];this.setDirectedEdges(t,e)}}function wr(){this.outEdges=new I,this.sorted=!1}function Lr(){if(Ir.apply(this),this.pt=null,this.deStar=null,1===arguments.length){var t=arguments[0];Lr.call(this,t,new wr)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.pt=e,this.deStar=n}}function Rr(){Sr.apply(this),this.line=null;var t=arguments[0];this.line=t}function Tr(){this.nodeMap=new rt}function Pr(){this.edges=new J,this.dirEdges=new J,this.nodeMap=new Tr}function br(){Pr.apply(this)}function Or(){this.graph=new br,this.mergedLineStrings=null,this.factory=null,this.edgeStrings=null}function _r(){this.edgeRing=null,this.next=null,this.label=-1;var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];Nr.call(this,t,e,n,i)}function Mr(){Sr.apply(this),this.line=null;var t=arguments[0];this.line=t}function Dr(){this.factory=null,this.deList=new I,this.lowestEdge=null,this.ring=null,this.ringPts=null,this.holes=null,this.shell=null,this._isHole=null,this._isProcessed=!1,this._isIncludedSet=!1,this._isIncluded=!1;var t=arguments[0];this.factory=t}function Ar(){}function Fr(){Pr.apply(this),this.factory=null;var t=arguments[0];this.factory=t}function Gr(){if(this.lineStringAdder=new qr(this),this.graph=null,this.dangles=new I,this.cutEdges=new I,this.invalidRingLines=new I,this.holeList=null,this.shellList=null,this.polyList=null,this.isCheckingRingsValid=!0,this.extractOnlyPolygonal=null,this.geomFactory=null,0===arguments.length)Gr.call(this,!1);else if(1===arguments.length){var t=arguments[0];this.extractOnlyPolygonal=t}}function qr(){this.p=null;var t=arguments[0];this.p=t}function Br(){}function zr(){if(this.edgeEnds=new I,1===arguments.length){var t=arguments[0];zr.call(this,null,t)}else if(2===arguments.length){var e=(arguments[0],arguments[1]);En.call(this,e.getEdge(),e.getCoordinate(),e.getDirectedCoordinate(),new gn(e.getLabel())),this.insert(e)}}function Vr(){Pn.apply(this)}function kr(){var t=arguments[0],e=arguments[1];yn.call(this,t,e)}function Yr(){Nn.apply(this)}function Ur(){this.li=new ae,this.ptLocator=new Te,this.arg=null,this.nodes=new xn(new Yr),this.im=null,this.isolatedEdges=new I,this.invalidPoint=null;var t=arguments[0];this.arg=t}function Xr(){this.rectEnv=null;var t=arguments[0];this.rectEnv=t.getEnvelopeInternal()}function Hr(){this.li=new ae,this.rectEnv=null,this.diagUp0=null,this.diagUp1=null,this.diagDown0=null,this.diagDown1=null;var t=arguments[0];this.rectEnv=t,this.diagUp0=new g(t.getMinX(),t.getMinY()),this.diagUp1=new g(t.getMaxX(),t.getMaxY()),this.diagDown0=new g(t.getMinX(),t.getMaxY()),this.diagDown1=new g(t.getMaxX(),t.getMinY())}function Wr(){this._isDone=!1}function jr(){this.rectangle=null,this.rectEnv=null;var t=arguments[0];this.rectangle=t,this.rectEnv=t.getEnvelopeInternal()}function Kr(){Wr.apply(this),this.rectEnv=null,this._intersects=!1;var t=arguments[0];this.rectEnv=t}function Zr(){Wr.apply(this),this.rectSeq=null,this.rectEnv=null,this._containsPoint=!1;var t=arguments[0];this.rectSeq=t.getExteriorRing().getCoordinateSequence(),this.rectEnv=t.getEnvelopeInternal()}function Qr(){Wr.apply(this),this.rectEnv=null,this.rectIntersector=null,this.hasIntersection=!1,this.p0=new g,this.p1=new g;var t=arguments[0];this.rectEnv=t.getEnvelopeInternal(),this.rectIntersector=new Hr(this.rectEnv)}function Jr(){if(this._relate=null,2===arguments.length){var t=arguments[0],e=arguments[1];ti.call(this,t,e),this._relate=new Ur(this.arg)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];ti.call(this,n,i,r),this._relate=new Ur(this.arg)}}function $r(){this.geomFactory=null,this.skipEmpty=!1,this.inputGeoms=null;var t=arguments[0];this.geomFactory=$r.extractFactory(t),this.inputGeoms=t}function ts(){this.pointGeom=null,this.otherGeom=null,this.geomFact=null;var t=arguments[0],e=arguments[1];this.pointGeom=t,this.otherGeom=e,this.geomFact=e.getFactory()}function es(){this.sortIndex=-1,this.comps=null;var t=arguments[0],e=arguments[1];this.sortIndex=t,this.comps=e}function ns(){this.inputPolys=null,this.geomFactory=null;var t=arguments[0];this.inputPolys=t,null===this.inputPolys&&(this.inputPolys=new I)}function is(){if(this.polygons=new I,this.lines=new I,this.points=new I,this.geomFact=null,1===arguments.length){if(R(arguments[0],v)){var t=arguments[0];this.extract(t)}else if(arguments[0]instanceof B){var e=arguments[0];this.extract(e)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.geomFact=i,this.extract(n)}}function rs(){this.geometryFactory=new ie,this.geomGraph=null,this.disconnectedRingcoord=null;var t=arguments[0];this.geomGraph=t}function ss(){this.nodes=new xn(new Yr)}function os(){this.li=new ae,this.geomGraph=null,this.nodeGraph=new ss,this.invalidPoint=null;var t=arguments[0];this.geomGraph=t}function as(){this.graph=null,this.rings=new I,this.totalEnv=new C,this.index=null,this.nestedPt=null;var t=arguments[0];this.graph=t}function us(){if(this.errorType=null,this.pt=null,1===arguments.length){var t=arguments[0];us.call(this,t,null)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.errorType=e,null!==n&&(this.pt=n.copy())}}function ls(){this.parentGeometry=null,this.isSelfTouchingRingFormingHoleValid=!1,this.validErr=null;var t=arguments[0];this.parentGeometry=t}function hs(){_t.CoordinateOperation.apply(this),this.targetPM=null,this.removeCollapsed=!0;var t=arguments[0],e=arguments[1];this.targetPM=t,this.removeCollapsed=e}function cs(){this.targetPM=null,this.removeCollapsed=!0,this.changePrecisionModel=!1,this.isPointwise=!1;var t=arguments[0];this.targetPM=t}function fs(){this.pts=null,this.usePt=null,this.distanceTolerance=null,this.seg=new ce;var t=arguments[0];this.pts=t}function gs(){this.inputGeom=null,this.distanceTolerance=null,this.isEnsureValidTopology=!0;var t=arguments[0];this.inputGeom=t}function ds(){xe.apply(this),this.isEnsureValidTopology=!0,this.distanceTolerance=null;var t=arguments[0],e=arguments[1];this.isEnsureValidTopology=t,this.distanceTolerance=e}function ps(){if(this.parent=null,this.index=null,2===arguments.length){var t=arguments[0],e=arguments[1];ps.call(this,t,e,null,-1)}else if(4===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2],s=arguments[3];ce.call(this,n,i),this.parent=r,this.index=s}}function vs(){if(this.parentLine=null,this.segs=null,this.resultSegs=new I,this.minimumSize=null,1===arguments.length){var t=arguments[0];vs.call(this,t,2)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.parentLine=e,this.minimumSize=n,this.init()}}function ms(){this.index=new Bi}function ys(){this.querySeg=null,this.items=new I;var t=arguments[0];this.querySeg=t}function xs(){this.li=new ae,this.inputIndex=new ms,this.outputIndex=new ms,this.line=null,this.linePts=null,this.distanceTolerance=0;var t=arguments[0],e=arguments[1];this.inputIndex=t,this.outputIndex=e}function Es(){this.inputIndex=new ms,this.outputIndex=new ms,this.distanceTolerance=0}function Is(){this.inputGeom=null,this.lineSimplifier=new Es,this.linestringMap=null;var t=arguments[0];this.inputGeom=t}function Ns(){xe.apply(this),this.linestringMap=null;var t=arguments[0];this.linestringMap=t}function Cs(){this.tps=null;var t=arguments[0];this.tps=t}function Ss(){this.seg=null,this.segLen=null,this.splitPt=null,this.minimumLen=0;var t=arguments[0];this.seg=t,this.segLen=t.getLength()}function ws(){}function Ls(){}function Rs(){}function Ts(){if(this.p=null,1===arguments.length){var t=arguments[0];this.p=new g(t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.p=new g(e,n)}else if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];this.p=new g(i,r,s)}}function Ps(){this._isOnConstraint=null,this.constraint=null;var t=arguments[0];Ts.call(this,t)}function bs(){this._rot=null,this.vertex=null,this.next=null,this.data=null}function Os(){this.subdiv=null,this.isUsingTolerance=!1;var t=arguments[0];this.subdiv=t,this.isUsingTolerance=t.getTolerance()>0}function _s(){}function Ms(){this.subdiv=null,this.lastEdge=null;var t=arguments[0];this.subdiv=t,this.init()}function Ds(){if(this.seg=null,1===arguments.length){if("string"==typeof arguments[0]){var t=arguments[0];l.call(this,t)}else if(arguments[0]instanceof ce){var e=arguments[0];l.call(this,"Locate failed to converge (at edge: "+e+").  Possible causes include invalid Subdivision topology or very close sites"),this.seg=new ce(e)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];l.call(this,Ds.msgWithSpatial(n,i)),this.seg=new ce(i)}}function As(){}function Fs(){this.visitedKey=0,this.quadEdges=new I,this.startingEdge=null,this.tolerance=null,this.edgeCoincidenceTolerance=null,this.frameVertex=new Array(3).fill(null),this.frameEnv=null,this.locator=null,this.seg=new ce,this.triEdges=new Array(3).fill(null);var t=arguments[0],e=arguments[1];this.tolerance=e,this.edgeCoincidenceTolerance=e/Fs.EDGE_COINCIDENCE_TOL_FACTOR,this.createFrame(t),this.startingEdge=this.initSubdiv(),this.locator=new Ms(this)}function Gs(){}function qs(){this.triList=new I}function Bs(){this.triList=new I}function zs(){this.coordList=new N,this.triCoords=new I}function Vs(){if(this.ls=null,this.data=null,2===arguments.length){var t=arguments[0],e=arguments[1];this.ls=new ce(t,e)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.ls=new ce(n,i),this.data=r}else if(6===arguments.length){var s=arguments[0],o=arguments[1],a=arguments[2],u=arguments[3],l=arguments[4],h=arguments[5];Vs.call(this,new g(s,o,a),new g(u,l,h))}else if(7===arguments.length){var c=arguments[0],f=arguments[1],d=arguments[2],p=arguments[3],v=arguments[4],m=arguments[5],y=arguments[6];Vs.call(this,new g(c,f,d),new g(p,v,m),y)}}function ks(){}function Ys(){if(this.p=null,this.data=null,this.left=null,this.right=null,this.count=null,2===arguments.length){var t=arguments[0],e=arguments[1];this.p=new g(t),this.left=null,this.right=null,this.count=1,this.data=e}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.p=new g(n,i),this.left=null,this.right=null,this.count=1,this.data=r}}function Us(){if(this.root=null,this.numberOfNodes=null,this.tolerance=null,0===arguments.length)Us.call(this,0);else if(1===arguments.length){var t=arguments[0];this.tolerance=t}}function Xs(){this.tolerance=null,this.matchNode=null,this.matchDist=0,this.p=null;var t=arguments[0],e=arguments[1];this.p=t,this.tolerance=e}function Hs(){this.initialVertices=null,this.segVertices=null,this.segments=new I,this.subdiv=null,this.incDel=null,this.convexHull=null,this.splitFinder=new Ls,this.kdt=null,this.vertexFactory=null,this.computeAreaEnv=null,this.splitPt=null,this.tolerance=null;var t=arguments[0],e=arguments[1];this.initialVertices=new I(t),this.tolerance=e,this.kdt=new Us(e)}function Ws(){this.siteCoords=null,this.tolerance=0,this.subdiv=null}function js(){this.siteCoords=null,this.constraintLines=null,this.tolerance=0,this.subdiv=null,this.constraintVertexMap=new rt}function Ks(){this.siteCoords=null,this.tolerance=0,this.subdiv=null,this.clipEnv=null,this.diagramEnv=null}function Zs(){}Array.prototype.fill||(Array.prototype.fill=function(t){for(var e=Object(this),n=parseInt(e.length,10),i=arguments[1],r=parseInt(i,10)||0,s=0>r?Math.max(n+r,0):Math.min(r,n),o=arguments[2],a=void 0===o?n:parseInt(o,10)||0,u=0>a?Math.max(n+a,0):Math.min(a,n);u>s;s++)e[s]=t;return e}),Number.isFinite=Number.isFinite||function(t){return"number"==typeof t&&isFinite(t)},Number.isInteger=Number.isInteger||function(t){return"number"==typeof t&&isFinite(t)&&Math.floor(t)===t},Number.parseFloat=Number.parseFloat||parseFloat,Number.isNaN=Number.isNaN||function(t){return t!==t},Math.trunc=Math.trunc||function(t){return 0>t?Math.ceil(t):Math.floor(t)},e(n.prototype,{interfaces_:function(){return[]},getClass:function(){return n}}),n.equalsWithTolerance=function(t,e,n){return Math.abs(t-e)<=n},r.isNaN=function(t){return Number.isNaN(t)},r.doubleToLongBits=function(t){return t},r.longBitsToDouble=function(t){return t},r.isInfinite=function(t){return!Number.isFinite(t)},r.MAX_VALUE=Number.MAX_VALUE,l.prototype=Object.create(Error.prototype),l.prototype.constructor=Error,h(c,l),e(c.prototype,{interfaces_:function(){return[]},getClass:function(){return c}}),e(f.prototype,{interfaces_:function(){return[]},getClass:function(){return f}}),f.shouldNeverReachHere=function(){if(0===arguments.length)f.shouldNeverReachHere(null);else if(1===arguments.length){var t=arguments[0];throw new c("Should never reach here"+(null!==t?": "+t:""))}},f.isTrue=function(){if(1===arguments.length){var t=arguments[0];f.isTrue(t,null)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(!e)throw null===n?new c:new c(n)}},f.equals=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];f.equals(t,e,null)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];if(!i.equals(n))throw new c("Expected "+n+" but encountered "+i+(null!==r?": "+r:""))}},e(g.prototype,{setOrdinate:function(t,e){switch(t){case g.X:this.x=e;break;case g.Y:this.y=e;break;case g.Z:this.z=e;break;default:throw new i("Invalid ordinate index: "+t)}},equals2D:function(){if(1===arguments.length){var t=arguments[0];return this.x!==t.x?!1:this.y===t.y}if(2===arguments.length){var e=arguments[0],i=arguments[1];return n.equalsWithTolerance(this.x,e.x,i)?!!n.equalsWithTolerance(this.y,e.y,i):!1}},getOrdinate:function(t){switch(t){case g.X:return this.x;case g.Y:return this.y;case g.Z:return this.z}throw new i("Invalid ordinate index: "+t)},equals3D:function(t){return this.x===t.x&&this.y===t.y&&(this.z===t.z||r.isNaN(this.z)&&r.isNaN(t.z))},equals:function(t){return t instanceof g?this.equals2D(t):!1},equalInZ:function(t,e){return n.equalsWithTolerance(this.z,t.z,e)},compareTo:function(t){var e=t;return this.x<e.x?-1:this.x>e.x?1:this.y<e.y?-1:this.y>e.y?1:0},clone:function(){try{var t=null;return t}catch(e){if(e instanceof CloneNotSupportedException)return f.shouldNeverReachHere("this shouldn't happen because this class is Cloneable"),null;throw e}finally{}},copy:function(){return new g(this)},toString:function(){return"("+this.x+", "+this.y+", "+this.z+")"},distance3D:function(t){var e=this.x-t.x,n=this.y-t.y,i=this.z-t.z;return Math.sqrt(e*e+n*n+i*i)},distance:function(t){var e=this.x-t.x,n=this.y-t.y;return Math.sqrt(e*e+n*n)},hashCode:function(){var t=17;return t=37*t+g.hashCode(this.x),t=37*t+g.hashCode(this.y)},setCoordinate:function(t){this.x=t.x,this.y=t.y,this.z=t.z},interfaces_:function(){return[s,o,u]},getClass:function(){return g}}),g.hashCode=function(){if(1===arguments.length){var t=arguments[0],e=r.doubleToLongBits(t);return Math.trunc(e^e>>>32)}},e(d.prototype,{compare:function(t,e){var n=t,i=e,r=d.compare(n.x,i.x);if(0!==r)return r;var s=d.compare(n.y,i.y);if(0!==s)return s;if(this.dimensionsToTest<=2)return 0;var o=d.compare(n.z,i.z);return o},interfaces_:function(){return[a]},getClass:function(){return d}}),d.compare=function(t,e){return e>t?-1:t>e?1:r.isNaN(t)?r.isNaN(e)?0:-1:r.isNaN(e)?1:0},g.DimensionalComparator=d,g.serialVersionUID=0x5cbf2c235c7e5800,g.NULL_ORDINATE=r.NaN,g.X=0,g.Y=1,g.Z=2,p.prototype.hasNext=function(){},p.prototype.next=function(){},p.prototype.remove=function(){},v.prototype.add=function(){},v.prototype.addAll=function(){},v.prototype.isEmpty=function(){},v.prototype.iterator=function(){},v.prototype.size=function(){},v.prototype.toArray=function(){},v.prototype.remove=function(){},m.prototype=new Error,m.prototype.name="IndexOutOfBoundsException",y.prototype=Object.create(v.prototype),y.prototype.constructor=y,y.prototype.get=function(){},y.prototype.set=function(){},y.prototype.isEmpty=function(){},x.prototype=new Error,x.prototype.name="NoSuchElementException",E.prototype=new Error,E.prototype.name="OperationNotSupported",I.prototype=Object.create(y.prototype),I.prototype.constructor=I,I.prototype.ensureCapacity=function(){},I.prototype.interfaces_=function(){return[y,v]},I.prototype.add=function(t){return 1===arguments.length?this.array_.push(t):this.array_.splice(arguments[0],arguments[1]),!0},I.prototype.clear=function(){this.array_=[]},I.prototype.addAll=function(t){for(var e=t.iterator();e.hasNext();)this.add(e.next());return!0},I.prototype.set=function(t,e){var n=this.array_[t];return this.array_[t]=e,n},I.prototype.iterator=function(){return new Qs(this)},I.prototype.get=function(t){if(0>t||t>=this.size())throw new m;return this.array_[t]},I.prototype.isEmpty=function(){return 0===this.array_.length},I.prototype.size=function(){return this.array_.length},I.prototype.toArray=function(){for(var t=[],e=0,n=this.array_.length;n>e;e++)t.push(this.array_[e]);return t},I.prototype.remove=function(t){for(var e=!1,n=0,i=this.array_.length;i>n;n++)if(this.array_[n]===t){this.array_.splice(n,1),e=!0;break}return e};var Qs=function(t){this.arrayList_=t,this.position_=0};Qs.prototype.next=function(){if(this.position_===this.arrayList_.size())throw new x;return this.arrayList_.get(this.position_++)},Qs.prototype.hasNext=function(){return this.position_<this.arrayList_.size()},Qs.prototype.set=function(t){return this.arrayList_.set(this.position_-1,t)},Qs.prototype.remove=function(){throw new E},h(N,I),e(N.prototype,{getCoordinate:function(t){return this.get(t)},addAll:function(){if(2===arguments.length){for(var t=arguments[0],e=arguments[1],n=!1,i=t.iterator();i.hasNext();)this.add(i.next(),e),n=!0;return n}return I.prototype.addAll.apply(this,arguments)},clone:function Go(){for(var Go=I.prototype.clone.call(this),t=0;t<this.size();t++)Go.add(t,this.get(t).copy());return Go},toCoordinateArray:function(){return this.toArray(N.coordArrayType)},add:function(){if(1===arguments.length){var t=arguments[0];I.prototype.add.call(this,t)}else if(2===arguments.length){if(arguments[0]instanceof Array&&"boolean"==typeof arguments[1]){var e=arguments[0],n=arguments[1];return this.add(e,n,!0),!0}if(arguments[0]instanceof g&&"boolean"==typeof arguments[1]){var i=arguments[0],r=arguments[1];if(!r&&this.size()>=1){var s=this.get(this.size()-1);if(s.equals2D(i))return null}I.prototype.add.call(this,i)}else if(arguments[0]instanceof Object&&"boolean"==typeof arguments[1]){var o=arguments[0],a=arguments[1];return this.add(o,a),!0}}else if(3===arguments.length){if("boolean"==typeof arguments[2]&&arguments[0]instanceof Array&&"boolean"==typeof arguments[1]){var u=arguments[0],l=arguments[1],h=arguments[2];if(h)for(var c=0;c<u.length;c++)this.add(u[c],l);else for(var c=u.length-1;c>=0;c--)this.add(u[c],l);return!0}if("boolean"==typeof arguments[2]&&Number.isInteger(arguments[0])&&arguments[1]instanceof g){var f=arguments[0],d=arguments[1],p=arguments[2];if(!p){var v=this.size();if(v>0){if(f>0){var m=this.get(f-1);if(m.equals2D(d))return null}if(v>f){var y=this.get(f);if(y.equals2D(d))return null}}}I.prototype.add.call(this,f,d)}}else if(4===arguments.length){var x=arguments[0],E=arguments[1],N=arguments[2],C=arguments[3],S=1;N>C&&(S=-1);for(var c=N;c!==C;c+=S)this.add(x[c],E);return!0}},closeRing:function(){this.size()>0&&this.add(new g(this.get(0)),!1)},interfaces_:function(){return[]},getClass:function(){return N}}),N.coordArrayType=new Array(0).fill(null),e(C.prototype,{getArea:function(){return this.getWidth()*this.getHeight()},equals:function(t){if(!(t instanceof C))return!1;var e=t;return this.isNull()?e.isNull():this.maxx===e.getMaxX()&&this.maxy===e.getMaxY()&&this.minx===e.getMinX()&&this.miny===e.getMinY()},intersection:function(t){if(this.isNull()||t.isNull()||!this.intersects(t))return new C;var e=this.minx>t.minx?this.minx:t.minx,n=this.miny>t.miny?this.miny:t.miny,i=this.maxx<t.maxx?this.maxx:t.maxx,r=this.maxy<t.maxy?this.maxy:t.maxy;return new C(e,i,n,r)},isNull:function(){return this.maxx<this.minx},getMaxX:function(){return this.maxx},covers:function(){if(1===arguments.length){if(arguments[0]instanceof g){var t=arguments[0];return this.covers(t.x,t.y)}if(arguments[0]instanceof C){var e=arguments[0];return this.isNull()||e.isNull()?!1:e.getMinX()>=this.minx&&e.getMaxX()<=this.maxx&&e.getMinY()>=this.miny&&e.getMaxY()<=this.maxy}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];return this.isNull()?!1:n>=this.minx&&n<=this.maxx&&i>=this.miny&&i<=this.maxy}},intersects:function(){if(1===arguments.length){if(arguments[0]instanceof C){var t=arguments[0];return this.isNull()||t.isNull()?!1:!(t.minx>this.maxx||t.maxx<this.minx||t.miny>this.maxy||t.maxy<this.miny)}if(arguments[0]instanceof g){var e=arguments[0];return this.intersects(e.x,e.y)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];return this.isNull()?!1:!(n>this.maxx||n<this.minx||i>this.maxy||i<this.miny)}},getMinY:function(){return this.miny},getMinX:function(){return this.minx},expandToInclude:function(){if(1===arguments.length){if(arguments[0]instanceof g){var t=arguments[0];this.expandToInclude(t.x,t.y)}else if(arguments[0]instanceof C){var e=arguments[0];if(e.isNull())return null;this.isNull()?(this.minx=e.getMinX(),this.maxx=e.getMaxX(),this.miny=e.getMinY(),this.maxy=e.getMaxY()):(e.minx<this.minx&&(this.minx=e.minx),e.maxx>this.maxx&&(this.maxx=e.maxx),e.miny<this.miny&&(this.miny=e.miny),e.maxy>this.maxy&&(this.maxy=e.maxy))}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.isNull()?(this.minx=n,this.maxx=n,this.miny=i,this.maxy=i):(n<this.minx&&(this.minx=n),n>this.maxx&&(this.maxx=n),i<this.miny&&(this.miny=i),i>this.maxy&&(this.maxy=i))}},minExtent:function(){if(this.isNull())return 0;var t=this.getWidth(),e=this.getHeight();return e>t?t:e},getWidth:function(){return this.isNull()?0:this.maxx-this.minx},compareTo:function(t){var e=t;return this.isNull()?e.isNull()?0:-1:e.isNull()?1:this.minx<e.minx?-1:this.minx>e.minx?1:this.miny<e.miny?-1:this.miny>e.miny?1:this.maxx<e.maxx?-1:this.maxx>e.maxx?1:this.maxy<e.maxy?-1:this.maxy>e.maxy?1:0},translate:function(t,e){return this.isNull()?null:void this.init(this.getMinX()+t,this.getMaxX()+t,this.getMinY()+e,this.getMaxY()+e)},toString:function(){return"Env["+this.minx+" : "+this.maxx+", "+this.miny+" : "+this.maxy+"]"},setToNull:function(){this.minx=0,this.maxx=-1,this.miny=0,this.maxy=-1},getHeight:function(){return this.isNull()?0:this.maxy-this.miny},maxExtent:function(){if(this.isNull())return 0;var t=this.getWidth(),e=this.getHeight();return t>e?t:e},expandBy:function(){if(1===arguments.length){var t=arguments[0];this.expandBy(t,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(this.isNull())return null;this.minx-=e,this.maxx+=e,this.miny-=n,this.maxy+=n,(this.minx>this.maxx||this.miny>this.maxy)&&this.setToNull()}},contains:function(){if(1===arguments.length){if(arguments[0]instanceof C){var t=arguments[0];return this.covers(t)}if(arguments[0]instanceof g){var e=arguments[0];return this.covers(e)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];return this.covers(n,i)}},centre:function(){return this.isNull()?null:new g((this.getMinX()+this.getMaxX())/2,(this.getMinY()+this.getMaxY())/2)},init:function(){if(0===arguments.length)this.setToNull();else if(1===arguments.length){if(arguments[0]instanceof g){var t=arguments[0];this.init(t.x,t.x,t.y,t.y)}else if(arguments[0]instanceof C){var e=arguments[0];this.minx=e.minx,this.maxx=e.maxx,this.miny=e.miny,this.maxy=e.maxy}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.init(n.x,i.x,n.y,i.y)}else if(4===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],a=arguments[3];s>r?(this.minx=r,this.maxx=s):(this.minx=s,this.maxx=r),a>o?(this.miny=o,this.maxy=a):(this.miny=a,this.maxy=o)}},getMaxY:function(){return this.maxy},distance:function(t){if(this.intersects(t))return 0;var e=0;this.maxx<t.minx?e=t.minx-this.maxx:this.minx>t.maxx&&(e=this.minx-t.maxx);var n=0;return this.maxy<t.miny?n=t.miny-this.maxy:this.miny>t.maxy&&(n=this.miny-t.maxy),0===e?n:0===n?e:Math.sqrt(e*e+n*n)},hashCode:function(){var t=17;return t=37*t+g.hashCode(this.minx),t=37*t+g.hashCode(this.maxx),t=37*t+g.hashCode(this.miny),t=37*t+g.hashCode(this.maxy)},interfaces_:function(){return[s,u]},getClass:function(){return C}}),C.intersects=function(){if(3===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2];return n.x>=(t.x<e.x?t.x:e.x)&&n.x<=(t.x>e.x?t.x:e.x)&&n.y>=(t.y<e.y?t.y:e.y)&&n.y<=(t.y>e.y?t.y:e.y)}if(4===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2],o=arguments[3],a=Math.min(s.x,o.x),u=Math.max(s.x,o.x),l=Math.min(i.x,r.x),h=Math.max(i.x,r.x);return l>u?!1:a>h?!1:(a=Math.min(s.y,o.y),u=Math.max(s.y,o.y),l=Math.min(i.y,r.y),h=Math.max(i.y,r.y),l>u?!1:!(a>h))}},C.serialVersionUID=0x51845cd552189800,h(w,S),e(w.prototype,{interfaces_:function(){return[]},getClass:function(){return w}}),e(L.prototype,{interfaces_:function(){return[]},getClass:function(){return L}}),L.toLocationSymbol=function(t){switch(t){case L.EXTERIOR:return"e";case L.BOUNDARY:return"b";case L.INTERIOR:return"i";case L.NONE:return"-"}throw new i("Unknown location value: "+t)},L.INTERIOR=0,L.BOUNDARY=1,L.EXTERIOR=2,L.NONE=-1,e(T.prototype,{interfaces_:function(){return[]},getClass:function(){return T}}),T.log10=function(t){var e=Math.log(t);return r.isInfinite(e)?e:r.isNaN(e)?e:e/T.LOG_10},T.min=function(t,e,n,i){var r=t;return r>e&&(r=e),r>n&&(r=n),r>i&&(r=i),r},T.clamp=function(){if("number"==typeof arguments[2]&&"number"==typeof arguments[0]&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1],n=arguments[2];return e>t?e:t>n?n:t}if(Number.isInteger(arguments[2])&&Number.isInteger(arguments[0])&&Number.isInteger(arguments[1])){var i=arguments[0],r=arguments[1],s=arguments[2];return r>i?r:i>s?s:i}},T.wrap=function(t,e){return 0>t?e- -t%e:t%e},T.max=function(){
if(3===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],i=t;return e>i&&(i=e),n>i&&(i=n),i}if(4===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],a=arguments[3],i=r;return s>i&&(i=s),o>i&&(i=o),a>i&&(i=a),i}},T.average=function(t,e){return(t+e)/2},T.LOG_10=Math.log(10),P.prototype.append=function(t){this.str+=t},P.prototype.setCharAt=function(t,e){return this.str.substr(0,t)+e+this.str.substr(t+1)},P.prototype.toString=function(t){return this.str},b.prototype.intValue=function(){return this.value},b.prototype.compareTo=function(t){return this.value<t?-1:this.value>t?1:0},b.isNaN=function(t){return Number.isNaN(t)},O.isWhitespace=function(t){return 32>=t&&t>=0||127==t},O.toUpperCase=function(t){return t.toUpperCase()},e(_.prototype,{le:function(t){return this.hi<t.hi||this.hi===t.hi&&this.lo<=t.lo},extractSignificantDigits:function(t,e){var n=this.abs(),i=_.magnitude(n.hi),r=_.TEN.pow(i);n=n.divide(r),n.gt(_.TEN)?(n=n.divide(_.TEN),i+=1):n.lt(_.ONE)&&(n=n.multiply(_.TEN),i-=1);for(var s=i+1,o=new P,a=_.MAX_PRINT_DIGITS-1,u=0;a>=u;u++){t&&u===s&&o.append(".");var l=Math.trunc(n.hi);if(0>l)break;var h=!1,c=0;l>9?(h=!0,c="9"):c="0"+l,o.append(c),n=n.subtract(_.valueOf(l)).multiply(_.TEN),h&&n.selfAdd(_.TEN);var f=!0,g=_.magnitude(n.hi);if(0>g&&Math.abs(g)>=a-u&&(f=!1),!f)break}return e[0]=i,o.toString()},sqr:function(){return this.multiply(this)},doubleValue:function(){return this.hi+this.lo},subtract:function(){if(arguments[0]instanceof _){var t=arguments[0];return this.add(t.negate())}if("number"==typeof arguments[0]){var e=arguments[0];return this.add(-e)}},equals:function(){if(1===arguments.length){var t=arguments[0];return this.hi===t.hi&&this.lo===t.lo}},isZero:function(){return 0===this.hi&&0===this.lo},selfSubtract:function(){if(arguments[0]instanceof _){var t=arguments[0];return this.isNaN()?this:this.selfAdd(-t.hi,-t.lo)}if("number"==typeof arguments[0]){var e=arguments[0];return this.isNaN()?this:this.selfAdd(-e,0)}},getSpecialNumberString:function(){return this.isZero()?"0.0":this.isNaN()?"NaN ":null},min:function(t){return this.le(t)?this:t},selfDivide:function(){if(1===arguments.length){if(arguments[0]instanceof _){var t=arguments[0];return this.selfDivide(t.hi,t.lo)}if("number"==typeof arguments[0]){var e=arguments[0];return this.selfDivide(e,0)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1],r=null,s=null,o=null,a=null,u=null,l=null,h=null,c=null;return u=this.hi/n,l=_.SPLIT*u,r=l-u,c=_.SPLIT*n,r=l-r,s=u-r,o=c-n,h=u*n,o=c-o,a=n-o,c=r*o-h+r*a+s*o+s*a,l=(this.hi-h-c+this.lo-u*i)/n,c=u+l,this.hi=c,this.lo=u-c+l,this}},dump:function(){return"DD<"+this.hi+", "+this.lo+">"},divide:function(){if(arguments[0]instanceof _){var t=arguments[0],e=null,n=null,i=null,s=null,o=null,a=null,u=null,l=null;o=this.hi/t.hi,a=_.SPLIT*o,e=a-o,l=_.SPLIT*t.hi,e=a-e,n=o-e,i=l-t.hi,u=o*t.hi,i=l-i,s=t.hi-i,l=e*i-u+e*s+n*i+n*s,a=(this.hi-u-l+this.lo-o*t.lo)/t.hi,l=o+a;var h=l,c=o-l+a;return new _(h,c)}if("number"==typeof arguments[0]){var f=arguments[0];return r.isNaN(f)?_.createNaN():_.copy(this).selfDivide(f,0)}},ge:function(t){return this.hi>t.hi||this.hi===t.hi&&this.lo>=t.lo},pow:function(t){if(0===t)return _.valueOf(1);var e=new _(this),n=_.valueOf(1),i=Math.abs(t);if(i>1)for(;i>0;)i%2===1&&n.selfMultiply(e),i/=2,i>0&&(e=e.sqr());else n=e;return 0>t?n.reciprocal():n},ceil:function(){if(this.isNaN())return _.NaN;var t=Math.ceil(this.hi),e=0;return t===this.hi&&(e=Math.ceil(this.lo)),new _(t,e)},compareTo:function(t){var e=t;return this.hi<e.hi?-1:this.hi>e.hi?1:this.lo<e.lo?-1:this.lo>e.lo?1:0},rint:function(){if(this.isNaN())return this;var t=this.add(.5);return t.floor()},setValue:function(){if(arguments[0]instanceof _){var t=arguments[0];return this.init(t),this}if("number"==typeof arguments[0]){var e=arguments[0];return this.init(e),this}},max:function(t){return this.ge(t)?this:t},sqrt:function(){if(this.isZero())return _.valueOf(0);if(this.isNegative())return _.NaN;var t=1/Math.sqrt(this.hi),e=this.hi*t,n=_.valueOf(e),i=this.subtract(n.sqr()),r=i.hi*(.5*t);return n.add(r)},selfAdd:function(){if(1===arguments.length){if(arguments[0]instanceof _){var t=arguments[0];return this.selfAdd(t.hi,t.lo)}if("number"==typeof arguments[0]){var e=arguments[0],n=null,i=null,r=null,s=null,o=null,a=null;return r=this.hi+e,o=r-this.hi,s=r-o,s=e-o+(this.hi-s),a=s+this.lo,n=r+a,i=a+(r-n),this.hi=n+i,this.lo=i+(n-this.hi),this}}else if(2===arguments.length){var u=arguments[0],l=arguments[1],n=null,i=null,h=null,c=null,r=null,s=null,o=null,a=null;r=this.hi+u,h=this.lo+l,o=r-this.hi,a=h-this.lo,s=r-o,c=h-a,s=u-o+(this.hi-s),c=l-a+(this.lo-c),o=s+h,n=r+o,i=o+(r-n),o=c+i;var f=n+o,g=o+(n-f);return this.hi=f,this.lo=g,this}},selfMultiply:function(){if(1===arguments.length){if(arguments[0]instanceof _){var t=arguments[0];return this.selfMultiply(t.hi,t.lo)}if("number"==typeof arguments[0]){var e=arguments[0];return this.selfMultiply(e,0)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1],r=null,s=null,o=null,a=null,u=null,l=null;u=_.SPLIT*this.hi,r=u-this.hi,l=_.SPLIT*n,r=u-r,s=this.hi-r,o=l-n,u=this.hi*n,o=l-o,a=n-o,l=r*o-u+r*a+s*o+s*a+(this.hi*i+this.lo*n);var h=u+l;r=u-h;var c=l+r;return this.hi=h,this.lo=c,this}},selfSqr:function(){return this.selfMultiply(this)},floor:function(){if(this.isNaN())return _.NaN;var t=Math.floor(this.hi),e=0;return t===this.hi&&(e=Math.floor(this.lo)),new _(t,e)},negate:function(){return this.isNaN()?this:new _(-this.hi,-this.lo)},clone:function(){try{return null}catch(t){if(t instanceof CloneNotSupportedException)return null;throw t}finally{}},multiply:function(){if(arguments[0]instanceof _){var t=arguments[0];return t.isNaN()?_.createNaN():_.copy(this).selfMultiply(t)}if("number"==typeof arguments[0]){var e=arguments[0];return r.isNaN(e)?_.createNaN():_.copy(this).selfMultiply(e,0)}},isNaN:function(){return r.isNaN(this.hi)},intValue:function(){return Math.trunc(this.hi)},toString:function(){var t=_.magnitude(this.hi);return t>=-3&&20>=t?this.toStandardNotation():this.toSciNotation()},toStandardNotation:function(){var t=this.getSpecialNumberString();if(null!==t)return t;var e=new Array(1).fill(null),n=this.extractSignificantDigits(!0,e),i=e[0]+1,r=n;if("."===n.charAt(0))r="0"+n;else if(0>i)r="0."+_.stringOfChar("0",-i)+n;else if(-1===n.indexOf(".")){var s=i-n.length,o=_.stringOfChar("0",s);r=n+o+".0"}return this.isNegative()?"-"+r:r},reciprocal:function(){var t=null,e=null,n=null,i=null,r=null,s=null,o=null,a=null;r=1/this.hi,s=_.SPLIT*r,t=s-r,a=_.SPLIT*this.hi,t=s-t,e=r-t,n=a-this.hi,o=r*this.hi,n=a-n,i=this.hi-n,a=t*n-o+t*i+e*n+e*i,s=(1-o-a-r*this.lo)/this.hi;var u=r+s,l=r-u+s;return new _(u,l)},toSciNotation:function(){if(this.isZero())return _.SCI_NOT_ZERO;var t=this.getSpecialNumberString();if(null!==t)return t;var e=new Array(1).fill(null),n=this.extractSignificantDigits(!1,e),i=_.SCI_NOT_EXPONENT_CHAR+e[0];if("0"===n.charAt(0))throw new IllegalStateException("Found leading zero: "+n);var r="";n.length>1&&(r=n.substring(1));var s=n.charAt(0)+"."+r;return this.isNegative()?"-"+s+i:s+i},abs:function(){return this.isNaN()?_.NaN:this.isNegative()?this.negate():new _(this)},isPositive:function(){return this.hi>0||0===this.hi&&this.lo>0},lt:function(t){return this.hi<t.hi||this.hi===t.hi&&this.lo<t.lo},add:function(){if(arguments[0]instanceof _){var t=arguments[0];return _.copy(this).selfAdd(t)}if("number"==typeof arguments[0]){var e=arguments[0];return _.copy(this).selfAdd(e)}},init:function(){if(1===arguments.length){if("number"==typeof arguments[0]){var t=arguments[0];this.hi=t,this.lo=0}else if(arguments[0]instanceof _){var e=arguments[0];this.hi=e.hi,this.lo=e.lo}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];this.hi=n,this.lo=i}},gt:function(t){return this.hi>t.hi||this.hi===t.hi&&this.lo>t.lo},isNegative:function(){return this.hi<0||0===this.hi&&this.lo<0},trunc:function(){return this.isNaN()?_.NaN:this.isPositive()?this.floor():this.ceil()},signum:function(){return this.hi>0?1:this.hi<0?-1:this.lo>0?1:this.lo<0?-1:0},interfaces_:function(){return[u,s,o]},getClass:function(){return _}}),_.sqr=function(t){return _.valueOf(t).selfMultiply(t)},_.valueOf=function(){if("string"==typeof arguments[0]){var t=arguments[0];return _.parse(t)}if("number"==typeof arguments[0]){var e=arguments[0];return new _(e)}},_.sqrt=function(t){return _.valueOf(t).sqrt()},_.parse=function(t){for(var e=0,n=t.length;O.isWhitespace(t.charAt(e));)e++;var i=!1;if(n>e){var r=t.charAt(e);"-"!==r&&"+"!==r||(e++,"-"===r&&(i=!0))}for(var s=new _,o=0,a=0,u=0;;){if(e>=n)break;var l=t.charAt(e);if(e++,O.isDigit(l)){var h=l-"0";s.selfMultiply(_.TEN),s.selfAdd(h),o++}else{if("."!==l){if("e"===l||"E"===l){var c=t.substring(e);try{u=b.parseInt(c)}catch(f){throw f instanceof NumberFormatException?new NumberFormatException("Invalid exponent "+c+" in string "+t):f}finally{}break}throw new NumberFormatException("Unexpected character '"+l+"' at position "+e+" in string "+t)}a=o}}var g=s,d=o-a-u;if(0===d)g=s;else if(d>0){var p=_.TEN.pow(d);g=s.divide(p)}else if(0>d){var p=_.TEN.pow(-d);g=s.multiply(p)}return i?g.negate():g},_.createNaN=function(){return new _(r.NaN,r.NaN)},_.copy=function(t){return new _(t)},_.magnitude=function(t){var e=Math.abs(t),n=Math.log(e)/Math.log(10),i=Math.trunc(Math.floor(n)),r=Math.pow(10,i);return e>=10*r&&(i+=1),i},_.stringOfChar=function(t,e){for(var n=new P,i=0;e>i;i++)n.append(t);return n.toString()},_.PI=new _(3.141592653589793,1.2246467991473532e-16),_.TWO_PI=new _(6.283185307179586,2.4492935982947064e-16),_.PI_2=new _(1.5707963267948966,6.123233995736766e-17),_.E=new _(2.718281828459045,1.4456468917292502e-16),_.NaN=new _(r.NaN,r.NaN),_.EPS=1.23259516440783e-32,_.SPLIT=134217729,_.MAX_PRINT_DIGITS=32,_.TEN=_.valueOf(10),_.ONE=_.valueOf(1),_.SCI_NOT_EXPONENT_CHAR="E",_.SCI_NOT_ZERO="0.0E0",e(M.prototype,{interfaces_:function(){return[]},getClass:function(){return M}}),M.orientationIndex=function(t,e,n){var i=M.orientationIndexFilter(t,e,n);if(1>=i)return i;var r=_.valueOf(e.x).selfAdd(-t.x),s=_.valueOf(e.y).selfAdd(-t.y),o=_.valueOf(n.x).selfAdd(-e.x),a=_.valueOf(n.y).selfAdd(-e.y);return r.selfMultiply(a).selfSubtract(s.selfMultiply(o)).signum()},M.signOfDet2x2=function(t,e,n,i){var r=t.multiply(i).selfSubtract(e.multiply(n));return r.signum()},M.intersection=function(t,e,n,i){var r=_.valueOf(i.y).selfSubtract(n.y).selfMultiply(_.valueOf(e.x).selfSubtract(t.x)),s=_.valueOf(i.x).selfSubtract(n.x).selfMultiply(_.valueOf(e.y).selfSubtract(t.y)),o=r.subtract(s),a=_.valueOf(i.x).selfSubtract(n.x).selfMultiply(_.valueOf(t.y).selfSubtract(n.y)),u=_.valueOf(i.y).selfSubtract(n.y).selfMultiply(_.valueOf(t.x).selfSubtract(n.x)),l=a.subtract(u),h=l.selfDivide(o).doubleValue(),c=_.valueOf(t.x).selfAdd(_.valueOf(e.x).selfSubtract(t.x).selfMultiply(h)).doubleValue(),f=_.valueOf(e.x).selfSubtract(t.x).selfMultiply(_.valueOf(t.y).selfSubtract(n.y)),d=_.valueOf(e.y).selfSubtract(t.y).selfMultiply(_.valueOf(t.x).selfSubtract(n.x)),p=f.subtract(d),v=p.selfDivide(o).doubleValue(),m=_.valueOf(n.y).selfAdd(_.valueOf(i.y).selfSubtract(n.y).selfMultiply(v)).doubleValue();return new g(c,m)},M.orientationIndexFilter=function(t,e,n){var i=null,r=(t.x-n.x)*(e.y-n.y),s=(t.y-n.y)*(e.x-n.x),o=r-s;if(r>0){if(0>=s)return M.signum(o);i=r+s}else{if(!(0>r))return M.signum(o);if(s>=0)return M.signum(o);i=-r-s}var a=M.DP_SAFE_EPSILON*i;return o>=a||-o>=a?M.signum(o):2},M.signum=function(t){return t>0?1:0>t?-1:0},M.DP_SAFE_EPSILON=1e-15,e(D.prototype,{setOrdinate:function(t,e,n){},size:function(){},getOrdinate:function(t,e){},getCoordinate:function(){if(1===arguments.length){arguments[0]}else if(2===arguments.length){arguments[0],arguments[1]}},getCoordinateCopy:function(t){},getDimension:function(){},getX:function(t){},clone:function(){},expandEnvelope:function(t){},copy:function(){},getY:function(t){},toCoordinateArray:function(){},interfaces_:function(){return[o]},getClass:function(){return D}}),D.X=0,D.Y=1,D.Z=2,D.M=3,A.arraycopy=function(t,e,n,i,r){for(var s=0,o=e;e+r>o;o++)n[i+s]=t[o],s++},A.getProperty=function(t){return{"line.separator":"\n"}[t]},e(F.prototype,{getY:function(){var t=this.y/this.w;if(r.isNaN(t)||r.isInfinite(t))throw new w;return t},getX:function(){var t=this.x/this.w;if(r.isNaN(t)||r.isInfinite(t))throw new w;return t},getCoordinate:function(){var t=new g;return t.x=this.getX(),t.y=this.getY(),t},interfaces_:function(){return[]},getClass:function(){return F}}),F.intersection=function(t,e,n,i){var s=t.y-e.y,o=e.x-t.x,a=t.x*e.y-e.x*t.y,u=n.y-i.y,l=i.x-n.x,h=n.x*i.y-i.x*n.y,c=o*h-l*a,f=u*a-s*h,d=s*l-u*o,p=c/d,v=f/d;if(r.isNaN(p)||r.isInfinite(p)||r.isNaN(v)||r.isInfinite(v))throw new w;return new g(p,v)},e(G.prototype,{create:function(){if(1===arguments.length){if(arguments[0]instanceof Array){arguments[0]}else if(R(arguments[0],D)){arguments[0]}}else if(2===arguments.length){arguments[0],arguments[1]}},interfaces_:function(){return[]},getClass:function(){return G}}),e(q.prototype,{filter:function(t){},interfaces_:function(){return[]},getClass:function(){return q}}),e(B.prototype,{isGeometryCollection:function(){return this.getSortIndex()===B.SORTINDEX_GEOMETRYCOLLECTION},getFactory:function(){return this.factory},getGeometryN:function(t){return this},getArea:function(){return 0},isRectangle:function(){return!1},equals:function(){if(1===arguments.length){if(arguments[0]instanceof B){var t=arguments[0];return null===t?!1:this.equalsTopo(t)}if(arguments[0]instanceof Object){var e=arguments[0];if(!(e instanceof B))return!1;var n=e;return this.equalsExact(n)}}},equalsExact:function(t){return this===t||this.equalsExact(t,0)},geometryChanged:function(){this.apply(B.geometryChangedFilter)},geometryChangedAction:function(){this.envelope=null},equalsNorm:function(t){return null===t?!1:this.norm().equalsExact(t.norm())},getLength:function(){return 0},getNumGeometries:function(){return 1},compareTo:function(){if(1===arguments.length){var t=arguments[0],e=t;return this.getSortIndex()!==e.getSortIndex()?this.getSortIndex()-e.getSortIndex():this.isEmpty()&&e.isEmpty()?0:this.isEmpty()?-1:e.isEmpty()?1:this.compareToSameClass(t)}if(2===arguments.length){var n=arguments[0],i=arguments[1],e=n;return this.getSortIndex()!==e.getSortIndex()?this.getSortIndex()-e.getSortIndex():this.isEmpty()&&e.isEmpty()?0:this.isEmpty()?-1:e.isEmpty()?1:this.compareToSameClass(n,i)}},getUserData:function(){return this.userData},getSRID:function(){return this.SRID},getEnvelope:function(){return this.getFactory().toGeometry(this.getEnvelopeInternal())},checkNotGeometryCollection:function(t){if(t.getSortIndex()===B.SORTINDEX_GEOMETRYCOLLECTION)throw new i("This method does not support GeometryCollection arguments")},equal:function(t,e,n){return 0===n?t.equals(e):t.distance(e)<=n},norm:function(){var t=this.copy();return t.normalize(),t},getPrecisionModel:function(){return this.factory.getPrecisionModel()},getEnvelopeInternal:function(){return null===this.envelope&&(this.envelope=this.computeEnvelopeInternal()),new C(this.envelope)},setSRID:function(t){this.SRID=t},setUserData:function(t){this.userData=t},compare:function(t,e){for(var n=t.iterator(),i=e.iterator();n.hasNext()&&i.hasNext();){var r=n.next(),s=i.next(),o=r.compareTo(s);if(0!==o)return o}return n.hasNext()?1:i.hasNext()?-1:0},hashCode:function(){return this.getEnvelopeInternal().hashCode()},isGeometryCollectionOrDerived:function(){return this.getSortIndex()===B.SORTINDEX_GEOMETRYCOLLECTION||this.getSortIndex()===B.SORTINDEX_MULTIPOINT||this.getSortIndex()===B.SORTINDEX_MULTILINESTRING||this.getSortIndex()===B.SORTINDEX_MULTIPOLYGON},interfaces_:function(){return[o,s,u]},getClass:function(){return B}}),B.hasNonEmptyElements=function(t){for(var e=0;e<t.length;e++)if(!t[e].isEmpty())return!0;return!1},B.hasNullElements=function(t){for(var e=0;e<t.length;e++)if(null===t[e])return!0;return!1},B.serialVersionUID=0x799ea46522854c00,B.SORTINDEX_POINT=0,B.SORTINDEX_MULTIPOINT=1,B.SORTINDEX_LINESTRING=2,B.SORTINDEX_LINEARRING=3,B.SORTINDEX_MULTILINESTRING=4,B.SORTINDEX_POLYGON=5,B.SORTINDEX_MULTIPOLYGON=6,B.SORTINDEX_GEOMETRYCOLLECTION=7,B.geometryChangedFilter={interfaces_:function(){return[q]},filter:function(t){t.geometryChangedAction()}},e(z.prototype,{filter:function(t){},interfaces_:function(){return[]},getClass:function(){return z}}),e(V.prototype,{isInBoundary:function(t){},interfaces_:function(){return[]},getClass:function(){return V}}),e(k.prototype,{isInBoundary:function(t){return t%2===1},interfaces_:function(){return[V]},getClass:function(){return k}}),e(Y.prototype,{isInBoundary:function(t){return t>0},interfaces_:function(){return[V]},getClass:function(){return Y}}),e(U.prototype,{isInBoundary:function(t){return t>1},interfaces_:function(){return[V]},getClass:function(){return U}}),e(X.prototype,{isInBoundary:function(t){return 1===t},interfaces_:function(){return[V]},getClass:function(){return X}}),V.Mod2BoundaryNodeRule=k,V.EndPointBoundaryNodeRule=Y,V.MultiValentEndPointBoundaryNodeRule=U,V.MonoValentEndPointBoundaryNodeRule=X,V.MOD2_BOUNDARY_RULE=new k,V.ENDPOINT_BOUNDARY_RULE=new Y,V.MULTIVALENT_ENDPOINT_BOUNDARY_RULE=new U,V.MONOVALENT_ENDPOINT_BOUNDARY_RULE=new X,V.OGC_SFS_BOUNDARY_RULE=V.MOD2_BOUNDARY_RULE,e(H.prototype,{interfaces_:function(){return[]},getClass:function(){return H}}),H.isRing=function(t){return t.length<4?!1:!!t[0].equals2D(t[t.length-1])},H.ptNotInList=function(t,e){for(var n=0;n<t.length;n++){var i=t[n];if(H.indexOf(i,e)<0)return i}return null},H.scroll=function(t,e){var n=H.indexOf(e,t);if(0>n)return null;var i=new Array(t.length).fill(null);A.arraycopy(t,n,i,0,t.length-n),A.arraycopy(t,0,i,t.length-n,n),A.arraycopy(i,0,t,0,t.length)},H.equals=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(t===e)return!0;if(null===t||null===e)return!1;if(t.length!==e.length)return!1;for(var n=0;n<t.length;n++)if(!t[n].equals(e[n]))return!1;return!0}if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];if(i===r)return!0;if(null===i||null===r)return!1;if(i.length!==r.length)return!1;for(var n=0;n<i.length;n++)if(0!==s.compare(i[n],r[n]))return!1;return!0}},H.intersection=function(t,e){for(var n=new N,i=0;i<t.length;i++)e.intersects(t[i])&&n.add(t[i],!0);return n.toCoordinateArray()},H.hasRepeatedPoints=function(t){for(var e=1;e<t.length;e++)if(t[e-1].equals(t[e]))return!0;return!1},H.removeRepeatedPoints=function(t){if(!H.hasRepeatedPoints(t))return t;var e=new N(t,!1);return e.toCoordinateArray()},H.reverse=function(t){for(var e=t.length-1,n=Math.trunc(e/2),i=0;n>=i;i++){var r=t[i];t[i]=t[e-i],t[e-i]=r}},H.removeNull=function(t){for(var e=0,n=0;n<t.length;n++)null!==t[n]&&e++;var i=new Array(e).fill(null);if(0===e)return i;for(var r=0,n=0;n<t.length;n++)null!==t[n]&&(i[r++]=t[n]);return i},H.copyDeep=function(){if(1===arguments.length){for(var t=arguments[0],e=new Array(t.length).fill(null),n=0;n<t.length;n++)e[n]=new g(t[n]);return e}if(5===arguments.length)for(var i=arguments[0],r=arguments[1],s=arguments[2],o=arguments[3],a=arguments[4],n=0;a>n;n++)s[o+n]=new g(i[r+n])},H.isEqualReversed=function(t,e){for(var n=0;n<t.length;n++){var i=t[n],r=e[t.length-n-1];if(0!==i.compareTo(r))return!1}return!0},H.envelope=function(t){for(var e=new C,n=0;n<t.length;n++)e.expandToInclude(t[n]);return e},H.toCoordinateArray=function(t){return t.toArray(H.coordArrayType)},H.atLeastNCoordinatesOrNothing=function(t,e){return e.length>=t?e:[]},H.indexOf=function(t,e){for(var n=0;n<e.length;n++)if(t.equals(e[n]))return n;return-1},H.increasingDirection=function(t){for(var e=0;e<Math.trunc(t.length/2);e++){var n=t.length-1-e,i=t[e].compareTo(t[n]);if(0!==i)return i}return 1},H.compare=function(t,e){for(var n=0;n<t.length&&n<e.length;){var i=t[n].compareTo(e[n]);if(0!==i)return i;n++}return n<e.length?-1:n<t.length?1:0},H.minCoordinate=function(t){for(var e=null,n=0;n<t.length;n++)(null===e||e.compareTo(t[n])>0)&&(e=t[n]);return e},H.extract=function(t,e,n){e=T.clamp(e,0,t.length),n=T.clamp(n,-1,t.length);var i=n-e+1;0>n&&(i=0),e>=t.length&&(i=0),e>n&&(i=0);var r=new Array(i).fill(null);if(0===i)return r;for(var s=0,o=e;n>=o;o++)r[s++]=t[o];return r},e(W.prototype,{compare:function(t,e){var n=t,i=e;return H.compare(n,i)},interfaces_:function(){return[a]},getClass:function(){return W}}),e(j.prototype,{compare:function(t,e){var n=t,i=e;if(n.length<i.length)return-1;if(n.length>i.length)return 1;if(0===n.length)return 0;var r=H.compare(n,i),s=H.isEqualReversed(n,i);return s?0:r},OLDcompare:function(t,e){var n=t,i=e;if(n.length<i.length)return-1;if(n.length>i.length)return 1;if(0===n.length)return 0;for(var r=H.increasingDirection(n),s=H.increasingDirection(i),o=r>0?0:n.length-1,a=s>0?0:n.length-1,u=0;u<n.length;u++){var l=n[o].compareTo(i[a]);if(0!==l)return l;o+=r,a+=s}return 0},interfaces_:function(){return[a]},getClass:function(){return j}}),H.ForwardComparator=W,H.BidirectionalComparator=j,H.coordArrayType=new Array(0).fill(null),K.prototype.get=function(){},K.prototype.put=function(){},K.prototype.size=function(){},K.prototype.values=function(){},K.prototype.entrySet=function(){},Z.prototype=new K,Q.prototype=new v,Q.prototype.contains=function(){},J.prototype=new Q,J.prototype.contains=function(t){for(var e=0,n=this.array_.length;n>e;e++){var i=this.array_[e];if(i===t)return!0}return!1},J.prototype.add=function(t){return this.contains(t)?!1:(this.array_.push(t),!0)},J.prototype.addAll=function(t){for(var e=t.iterator();e.hasNext();)this.add(e.next());return!0},J.prototype.remove=function(t){throw new javascript.util.OperationNotSupported},J.prototype.size=function(){return this.array_.length},J.prototype.isEmpty=function(){return 0===this.array_.length},J.prototype.toArray=function(){for(var t=[],e=0,n=this.array_.length;n>e;e++)t.push(this.array_[e]);return t},J.prototype.iterator=function(){return new Js(this)};var Js=function(t){this.hashSet_=t,this.position_=0};Js.prototype.next=function(){if(this.position_===this.hashSet_.size())throw new x;return this.hashSet_.array_[this.position_++]},Js.prototype.hasNext=function(){return this.position_<this.hashSet_.size()},Js.prototype.remove=function(){throw new E};var $s=0,to=1;rt.prototype=new Z,rt.prototype.get=function(t){for(var e=this.root_;null!==e;){var n=t.compareTo(e.key);if(0>n)e=e.left;else{if(!(n>0))return e.value;e=e.right}}return null},rt.prototype.put=function(t,e){if(null===this.root_)return this.root_={key:t,value:e,left:null,right:null,parent:null,color:$s,getValue:function(){return this.value},getKey:function(){return this.key}},this.size_=1,null;var n,i,r=this.root_;do if(n=r,i=t.compareTo(r.key),0>i)r=r.left;else{if(!(i>0)){var s=r.value;return r.value=e,s}r=r.right}while(null!==r);var o={key:t,left:null,right:null,value:e,parent:n,color:$s,getValue:function(){return this.value},getKey:function(){return this.key}};return 0>i?n.left=o:n.right=o,this.fixAfterInsertion(o),this.size_++,null},rt.prototype.fixAfterInsertion=function(t){for(t.color=to;null!=t&&t!=this.root_&&t.parent.color==to;)if(tt(t)==nt(tt(tt(t)))){var e=it(tt(tt(t)));$(e)==to?(et(tt(t),$s),et(e,$s),et(tt(tt(t)),to),t=tt(tt(t))):(t==it(tt(t))&&(t=tt(t),this.rotateLeft(t)),et(tt(t),$s),et(tt(tt(t)),to),this.rotateRight(tt(tt(t))))}else{var e=nt(tt(tt(t)));$(e)==to?(et(tt(t),$s),et(e,$s),et(tt(tt(t)),to),t=tt(tt(t))):(t==nt(tt(t))&&(t=tt(t),this.rotateRight(t)),et(tt(t),$s),et(tt(tt(t)),to),this.rotateLeft(tt(tt(t))))}this.root_.color=$s},rt.prototype.values=function(){var t=new I,e=this.getFirstEntry();if(null!==e)for(t.add(e.value);null!==(e=rt.successor(e));)t.add(e.value);return t},rt.prototype.entrySet=function(){var t=new J,e=this.getFirstEntry();if(null!==e)for(t.add(e);null!==(e=rt.successor(e));)t.add(e);return t},rt.prototype.rotateLeft=function(t){if(null!=t){var e=t.right;t.right=e.left,null!=e.left&&(e.left.parent=t),e.parent=t.parent,null==t.parent?this.root_=e:t.parent.left==t?t.parent.left=e:t.parent.right=e,e.left=t,t.parent=e}},rt.prototype.rotateRight=function(t){if(null!=t){var e=t.left;t.left=e.right,null!=e.right&&(e.right.parent=t),e.parent=t.parent,null==t.parent?this.root_=e:t.parent.right==t?t.parent.right=e:t.parent.left=e,e.right=t,t.parent=e}},rt.prototype.getFirstEntry=function(){var t=this.root_;if(null!=t)for(;null!=t.left;)t=t.left;return t},rt.successor=function(t){if(null===t)return null;if(null!==t.right){for(var e=t.right;null!==e.left;)e=e.left;return e}for(var e=t.parent,n=t;null!==e&&n===e.right;)n=e,e=e.parent;return e},rt.prototype.size=function(){return this.size_},e(st.prototype,{interfaces_:function(){return[]},getClass:function(){return st}}),ot.prototype=new Q,at.prototype=new ot,at.prototype.contains=function(t){for(var e=0,n=this.array_.length;n>e;e++){var i=this.array_[e];if(0===i.compareTo(t))return!0}return!1},at.prototype.add=function(t){if(this.contains(t))return!1;for(var e=0,n=this.array_.length;n>e;e++){var i=this.array_[e];if(1===i.compareTo(t))return this.array_.splice(e,0,t),!0}return this.array_.push(t),!0},at.prototype.addAll=function(t){for(var e=t.iterator();e.hasNext();)this.add(e.next());return!0},at.prototype.remove=function(t){throw new E},at.prototype.size=function(){return this.array_.length},at.prototype.isEmpty=function(){return 0===this.array_.length},at.prototype.toArray=function(){for(var t=[],e=0,n=this.array_.length;n>e;e++)t.push(this.array_[e]);return t},at.prototype.iterator=function(){return new eo(this)};var eo=function(t){this.treeSet_=t,this.position_=0};eo.prototype.next=function(){if(this.position_===this.treeSet_.size())throw new x;return this.treeSet_.array_[this.position_++]},eo.prototype.hasNext=function(){return this.position_<this.treeSet_.size()},eo.prototype.remove=function(){throw new E},ut.sort=function(){var t,e,n,i,r=arguments[0];if(1===arguments.length)return i=function(t,e){return t.compareTo(e)},void r.sort(i);if(2===arguments.length)n=arguments[1],i=function(t,e){return n.compare(t,e)},r.sort(i);else{if(3===arguments.length){e=r.slice(arguments[1],arguments[2]),e.sort();var s=r.slice(0,arguments[1]).concat(e,r.slice(arguments[2],r.length));for(r.splice(0,r.length),t=0;t<s.length;t++)r.push(s[t]);return}if(4===arguments.length){for(e=r.slice(arguments[1],arguments[2]),n=arguments[3],i=function(t,e){return n.compare(t,e)},e.sort(i),s=r.slice(0,arguments[1]).concat(e,r.slice(arguments[2],r.length)),r.splice(0,r.length),t=0;t<s.length;t++)r.push(s[t]);return}}},ut.asList=function(t){for(var e=new I,n=0,i=t.length;i>n;n++)e.add(t[n]);return e},e(lt.prototype,{interfaces_:function(){return[]},getClass:function(){return lt}}),lt.toDimensionSymbol=function(t){switch(t){case lt.FALSE:return lt.SYM_FALSE;case lt.TRUE:return lt.SYM_TRUE;case lt.DONTCARE:return lt.SYM_DONTCARE;case lt.P:return lt.SYM_P;case lt.L:return lt.SYM_L;case lt.A:return lt.SYM_A}throw new i("Unknown dimension value: "+t)},lt.toDimensionValue=function(t){switch(O.toUpperCase(t)){case lt.SYM_FALSE:return lt.FALSE;case lt.SYM_TRUE:return lt.TRUE;case lt.SYM_DONTCARE:return lt.DONTCARE;case lt.SYM_P:return lt.P;case lt.SYM_L:return lt.L;case lt.SYM_A:return lt.A}throw new i("Unknown dimension symbol: "+t)},lt.P=0,lt.L=1,lt.A=2,lt.FALSE=-1,lt.TRUE=-2,lt.DONTCARE=-3,lt.SYM_FALSE="F",lt.SYM_TRUE="T",lt.SYM_DONTCARE="*",lt.SYM_P="0",lt.SYM_L="1",lt.SYM_A="2",e(ht.prototype,{filter:function(t){},interfaces_:function(){return[]},getClass:function(){return ht}}),e(ct.prototype,{filter:function(t,e){},isDone:function(){},isGeometryChanged:function(){},interfaces_:function(){return[]},getClass:function(){return ct}}),h(ft,B),e(ft.prototype,{computeEnvelopeInternal:function(){for(var t=new C,e=0;e<this.geometries.length;e++)t.expandToInclude(this.geometries[e].getEnvelopeInternal());return t},getGeometryN:function(t){return this.geometries[t]},getSortIndex:function(){return B.SORTINDEX_GEOMETRYCOLLECTION},getCoordinates:function(){for(var t=new Array(this.getNumPoints()).fill(null),e=-1,n=0;n<this.geometries.length;n++)for(var i=this.geometries[n].getCoordinates(),r=0;r<i.length;r++)e++,t[e]=i[r];return t},getArea:function(){for(var t=0,e=0;e<this.geometries.length;e++)t+=this.geometries[e].getArea();return t},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(!this.isEquivalentClass(t))return!1;var n=t;if(this.geometries.length!==n.geometries.length)return!1;for(var i=0;i<this.geometries.length;i++)if(!this.geometries[i].equalsExact(n.geometries[i],e))return!1;return!0}return B.prototype.equalsExact.apply(this,arguments)},normalize:function(){for(var t=0;t<this.geometries.length;t++)this.geometries[t].normalize();ut.sort(this.geometries)},getCoordinate:function(){return this.isEmpty()?null:this.geometries[0].getCoordinate()},getBoundaryDimension:function(){for(var t=lt.FALSE,e=0;e<this.geometries.length;e++)t=Math.max(t,this.geometries[e].getBoundaryDimension());return t},getDimension:function(){for(var t=lt.FALSE,e=0;e<this.geometries.length;e++)t=Math.max(t,this.geometries[e].getDimension());return t},getLength:function(){for(var t=0,e=0;e<this.geometries.length;e++)t+=this.geometries[e].getLength();return t},getNumPoints:function(){for(var t=0,e=0;e<this.geometries.length;e++)t+=this.geometries[e].getNumPoints();return t},getNumGeometries:function(){return this.geometries.length},reverse:function(){for(var t=this.geometries.length,e=new Array(t).fill(null),n=0;n<this.geometries.length;n++)e[n]=this.geometries[n].reverse();return this.getFactory().createGeometryCollection(e)},compareToSameClass:function(){if(1===arguments.length){var t=arguments[0],e=new at(ut.asList(this.geometries)),n=new at(ut.asList(t.geometries));return this.compare(e,n)}if(2===arguments.length){for(var i=arguments[0],r=arguments[1],s=i,o=this.getNumGeometries(),a=s.getNumGeometries(),u=0;o>u&&a>u;){var l=this.getGeometryN(u),h=s.getGeometryN(u),c=l.compareToSameClass(h,r);if(0!==c)return c;u++}return o>u?1:a>u?-1:0}},apply:function(){if(R(arguments[0],z))for(var t=arguments[0],e=0;e<this.geometries.length;e++)this.geometries[e].apply(t);else if(R(arguments[0],ct)){var n=arguments[0];if(0===this.geometries.length)return null;for(var e=0;e<this.geometries.length&&(this.geometries[e].apply(n),!n.isDone());e++);n.isGeometryChanged()&&this.geometryChanged()}else if(R(arguments[0],ht)){var i=arguments[0];i.filter(this);for(var e=0;e<this.geometries.length;e++)this.geometries[e].apply(i)}else if(R(arguments[0],q)){var r=arguments[0];r.filter(this);for(var e=0;e<this.geometries.length;e++)this.geometries[e].apply(r)}},getBoundary:function(){return this.checkNotGeometryCollection(this),f.shouldNeverReachHere(),null},clone:function(){var t=B.prototype.clone.call(this);t.geometries=new Array(this.geometries.length).fill(null);for(var e=0;e<this.geometries.length;e++)t.geometries[e]=this.geometries[e].clone();return t},getGeometryType:function(){return"GeometryCollection"},copy:function(){for(var t=new Array(this.geometries.length).fill(null),e=0;e<t.length;e++)t[e]=this.geometries[e].copy();return new ft(t,this.factory)},isEmpty:function(){for(var t=0;t<this.geometries.length;t++)if(!this.geometries[t].isEmpty())return!1;return!0},interfaces_:function(){return[]},getClass:function(){return ft}}),ft.serialVersionUID=-0x4f07bcb1f857d800,h(gt,ft),e(gt.prototype,{getSortIndex:function(){return B.SORTINDEX_MULTILINESTRING},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.isEquivalentClass(t)?ft.prototype.equalsExact.call(this,t,e):!1}return ft.prototype.equalsExact.apply(this,arguments)},getBoundaryDimension:function(){return this.isClosed()?lt.FALSE:0},isClosed:function(){if(this.isEmpty())return!1;for(var t=0;t<this.geometries.length;t++)if(!this.geometries[t].isClosed())return!1;return!0},getDimension:function(){return 1},reverse:function(){for(var t=this.geometries.length,e=new Array(t).fill(null),n=0;n<this.geometries.length;n++)e[t-1-n]=this.geometries[n].reverse();return this.getFactory().createMultiLineString(e)},getBoundary:function(){
return new dt(this).getBoundary()},getGeometryType:function(){return"MultiLineString"},copy:function(){for(var t=new Array(this.geometries.length).fill(null),e=0;e<t.length;e++)t[e]=this.geometries[e].copy();return new gt(t,this.factory)},interfaces_:function(){return[st]},getClass:function(){return gt}}),gt.serialVersionUID=0x7155d2ab4afa8000,e(dt.prototype,{boundaryMultiLineString:function(t){if(this.geom.isEmpty())return this.getEmptyMultiPoint();var e=this.computeBoundaryCoordinates(t);return 1===e.length?this.geomFact.createPoint(e[0]):this.geomFact.createMultiPointFromCoords(e)},getBoundary:function(){return this.geom instanceof St?this.boundaryLineString(this.geom):this.geom instanceof gt?this.boundaryMultiLineString(this.geom):this.geom.getBoundary()},boundaryLineString:function(t){if(this.geom.isEmpty())return this.getEmptyMultiPoint();if(t.isClosed()){var e=this.bnRule.isInBoundary(2);return e?t.getStartPoint():this.geomFact.createMultiPoint()}return this.geomFact.createMultiPoint([t.getStartPoint(),t.getEndPoint()])},getEmptyMultiPoint:function(){return this.geomFact.createMultiPoint()},computeBoundaryCoordinates:function(t){var e=new I;this.endpointMap=new rt;for(var n=0;n<t.getNumGeometries();n++){var i=t.getGeometryN(n);0!==i.getNumPoints()&&(this.addEndpoint(i.getCoordinateN(0)),this.addEndpoint(i.getCoordinateN(i.getNumPoints()-1)))}for(var r=this.endpointMap.entrySet().iterator();r.hasNext();){var s=r.next(),o=s.getValue(),a=o.count;this.bnRule.isInBoundary(a)&&e.add(s.getKey())}return H.toCoordinateArray(e)},addEndpoint:function(t){var e=this.endpointMap.get(t);null===e&&(e=new pt,this.endpointMap.put(t,e)),e.count++},interfaces_:function(){return[]},getClass:function(){return dt}}),dt.getBoundary=function(){if(1===arguments.length){var t=arguments[0],e=new dt(t);return e.getBoundary()}if(2===arguments.length){var n=arguments[0],i=arguments[1],e=new dt(n,i);return e.getBoundary()}},e(pt.prototype,{interfaces_:function(){return[]},getClass:function(){return pt}}),e(Nt.prototype,{interfaces_:function(){return[]},getClass:function(){return Nt}}),Nt.chars=function(t,e){for(var n=new Array(e).fill(null),i=0;e>i;i++)n[i]=t;return new String(n)},Nt.getStackTrace=function(){if(1===arguments.length){var t=arguments[0],e=new xt,n=new vt(e);return t.printStackTrace(n),e.toString()}if(2===arguments.length){for(var i=arguments[0],r=arguments[1],s="",o=new mt(Nt.getStackTrace(i)),a=new It(o),u=0;r>u;u++)try{s+=a.readLine()+Nt.NEWLINE}catch(l){if(!(l instanceof Et))throw l;f.shouldNeverReachHere()}finally{}return s}},Nt.split=function(t,e){for(var n=e.length,i=new I,r=""+t,s=r.indexOf(e);s>=0;){var o=r.substring(0,s);i.add(o),r=r.substring(s+n),s=r.indexOf(e)}r.length>0&&i.add(r);for(var a=new Array(i.size()).fill(null),u=0;u<a.length;u++)a[u]=i.get(u);return a},Nt.toString=function(){if(1===arguments.length){var t=arguments[0];return Nt.SIMPLE_ORDINATE_FORMAT.format(t)}},Nt.spaces=function(t){return Nt.chars(" ",t)},Nt.NEWLINE=A.getProperty("line.separator"),Nt.SIMPLE_ORDINATE_FORMAT=new yt("0.#"),e(Ct.prototype,{interfaces_:function(){return[]},getClass:function(){return Ct}}),Ct.copyCoord=function(t,e,n,i){for(var r=Math.min(t.getDimension(),n.getDimension()),s=0;r>s;s++)n.setOrdinate(i,s,t.getOrdinate(e,s))},Ct.isRing=function(t){var e=t.size();return 0===e?!0:3>=e?!1:t.getOrdinate(0,D.X)===t.getOrdinate(e-1,D.X)&&t.getOrdinate(0,D.Y)===t.getOrdinate(e-1,D.Y)},Ct.isEqual=function(t,e){var n=t.size(),i=e.size();if(n!==i)return!1;for(var s=Math.min(t.getDimension(),e.getDimension()),o=0;n>o;o++)for(var a=0;s>a;a++){var u=t.getOrdinate(o,a),l=e.getOrdinate(o,a);if(!(t.getOrdinate(o,a)===e.getOrdinate(o,a)||r.isNaN(u)&&r.isNaN(l)))return!1}return!0},Ct.extend=function(t,e,n){var i=t.create(n,e.getDimension()),r=e.size();if(Ct.copy(e,0,i,0,r),r>0)for(var s=r;n>s;s++)Ct.copy(e,r-1,i,s,1);return i},Ct.reverse=function(t){for(var e=t.size()-1,n=Math.trunc(e/2),i=0;n>=i;i++)Ct.swap(t,i,e-i)},Ct.swap=function(t,e,n){if(e===n)return null;for(var i=0;i<t.getDimension();i++){var r=t.getOrdinate(e,i);t.setOrdinate(e,i,t.getOrdinate(n,i)),t.setOrdinate(n,i,r)}},Ct.copy=function(t,e,n,i,r){for(var s=0;r>s;s++)Ct.copyCoord(t,e+s,n,i+s)},Ct.toString=function(){if(1===arguments.length){var t=arguments[0],e=t.size();if(0===e)return"()";var n=t.getDimension(),i=new P;i.append("(");for(var r=0;e>r;r++){r>0&&i.append(" ");for(var s=0;n>s;s++)s>0&&i.append(","),i.append(Nt.toString(t.getOrdinate(r,s)))}return i.append(")"),i.toString()}},Ct.ensureValidRing=function(t,e){var n=e.size();if(0===n)return e;if(3>=n)return Ct.createClosedRing(t,e,4);var i=e.getOrdinate(0,D.X)===e.getOrdinate(n-1,D.X)&&e.getOrdinate(0,D.Y)===e.getOrdinate(n-1,D.Y);return i?e:Ct.createClosedRing(t,e,n+1)},Ct.createClosedRing=function(t,e,n){var i=t.create(n,e.getDimension()),r=e.size();Ct.copy(e,0,i,0,r);for(var s=r;n>s;s++)Ct.copy(e,0,i,s,1);return i},h(St,B),e(St.prototype,{computeEnvelopeInternal:function(){return this.isEmpty()?new C:this.points.expandEnvelope(new C)},isRing:function(){return this.isClosed()&&this.isSimple()},getSortIndex:function(){return B.SORTINDEX_LINESTRING},getCoordinates:function(){return this.points.toCoordinateArray()},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(!this.isEquivalentClass(t))return!1;var n=t;if(this.points.size()!==n.points.size())return!1;for(var i=0;i<this.points.size();i++)if(!this.equal(this.points.getCoordinate(i),n.points.getCoordinate(i),e))return!1;return!0}return B.prototype.equalsExact.apply(this,arguments)},normalize:function(){for(var t=0;t<Math.trunc(this.points.size()/2);t++){var e=this.points.size()-1-t;if(!this.points.getCoordinate(t).equals(this.points.getCoordinate(e)))return this.points.getCoordinate(t).compareTo(this.points.getCoordinate(e))>0&&Ct.reverse(this.points),null}},getCoordinate:function(){return this.isEmpty()?null:this.points.getCoordinate(0)},getBoundaryDimension:function(){return this.isClosed()?lt.FALSE:0},isClosed:function(){return this.isEmpty()?!1:this.getCoordinateN(0).equals2D(this.getCoordinateN(this.getNumPoints()-1))},getEndPoint:function(){return this.isEmpty()?null:this.getPointN(this.getNumPoints()-1)},getDimension:function(){return 1},getLength:function(){return he.computeLength(this.points)},getNumPoints:function(){return this.points.size()},reverse:function(){var t=this.points.copy();Ct.reverse(t);var e=this.getFactory().createLineString(t);return e},compareToSameClass:function(){if(1===arguments.length){for(var t=arguments[0],e=t,n=0,i=0;n<this.points.size()&&i<e.points.size();){var r=this.points.getCoordinate(n).compareTo(e.points.getCoordinate(i));if(0!==r)return r;n++,i++}return n<this.points.size()?1:i<e.points.size()?-1:0}if(2===arguments.length){var s=arguments[0],o=arguments[1],e=s;return o.compare(this.points,e.points)}},apply:function(){if(R(arguments[0],z))for(var t=arguments[0],e=0;e<this.points.size();e++)t.filter(this.points.getCoordinate(e));else if(R(arguments[0],ct)){var n=arguments[0];if(0===this.points.size())return null;for(var e=0;e<this.points.size()&&(n.filter(this.points,e),!n.isDone());e++);n.isGeometryChanged()&&this.geometryChanged()}else if(R(arguments[0],ht)){var i=arguments[0];i.filter(this)}else if(R(arguments[0],q)){var r=arguments[0];r.filter(this)}},getBoundary:function(){return new dt(this).getBoundary()},isEquivalentClass:function(t){return t instanceof St},clone:function(){var t=B.prototype.clone.call(this);return t.points=this.points.clone(),t},getCoordinateN:function(t){return this.points.getCoordinate(t)},getGeometryType:function(){return"LineString"},copy:function(){return new St(this.points.copy(),this.factory)},getCoordinateSequence:function(){return this.points},isEmpty:function(){return 0===this.points.size()},init:function(t){if(null===t&&(t=this.getFactory().getCoordinateSequenceFactory().create([])),1===t.size())throw new i("Invalid number of points in LineString (found "+t.size()+" - must be 0 or >= 2)");this.points=t},isCoordinate:function(t){for(var e=0;e<this.points.size();e++)if(this.points.getCoordinate(e).equals(t))return!0;return!1},getStartPoint:function(){return this.isEmpty()?null:this.getPointN(0)},getPointN:function(t){return this.getFactory().createPoint(this.points.getCoordinate(t))},interfaces_:function(){return[st]},getClass:function(){return St}}),St.serialVersionUID=0x2b2b51ba435c8e00,e(wt.prototype,{interfaces_:function(){return[]},getClass:function(){return wt}}),h(Lt,B),e(Lt.prototype,{computeEnvelopeInternal:function(){if(this.isEmpty())return new C;var t=new C;return t.expandToInclude(this.coordinates.getX(0),this.coordinates.getY(0)),t},getSortIndex:function(){return B.SORTINDEX_POINT},getCoordinates:function(){return this.isEmpty()?[]:[this.getCoordinate()]},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.isEquivalentClass(t)?this.isEmpty()&&t.isEmpty()?!0:this.isEmpty()!==t.isEmpty()?!1:this.equal(t.getCoordinate(),this.getCoordinate(),e):!1}return B.prototype.equalsExact.apply(this,arguments)},normalize:function(){},getCoordinate:function(){return 0!==this.coordinates.size()?this.coordinates.getCoordinate(0):null},getBoundaryDimension:function(){return lt.FALSE},getDimension:function(){return 0},getNumPoints:function(){return this.isEmpty()?0:1},reverse:function(){return this.copy()},getX:function(){if(null===this.getCoordinate())throw new IllegalStateException("getX called on empty Point");return this.getCoordinate().x},compareToSameClass:function(){if(1===arguments.length){var t=arguments[0],e=t;return this.getCoordinate().compareTo(e.getCoordinate())}if(2===arguments.length){var n=arguments[0],i=arguments[1],e=n;return i.compare(this.coordinates,e.coordinates)}},apply:function(){if(R(arguments[0],z)){var t=arguments[0];if(this.isEmpty())return null;t.filter(this.getCoordinate())}else if(R(arguments[0],ct)){var e=arguments[0];if(this.isEmpty())return null;e.filter(this.coordinates,0),e.isGeometryChanged()&&this.geometryChanged()}else if(R(arguments[0],ht)){var n=arguments[0];n.filter(this)}else if(R(arguments[0],q)){var i=arguments[0];i.filter(this)}},getBoundary:function(){return this.getFactory().createGeometryCollection(null)},clone:function(){var t=B.prototype.clone.call(this);return t.coordinates=this.coordinates.clone(),t},getGeometryType:function(){return"Point"},copy:function(){return new Lt(this.coordinates.copy(),this.factory)},getCoordinateSequence:function(){return this.coordinates},getY:function(){if(null===this.getCoordinate())throw new IllegalStateException("getY called on empty Point");return this.getCoordinate().y},isEmpty:function(){return 0===this.coordinates.size()},init:function(t){null===t&&(t=this.getFactory().getCoordinateSequenceFactory().create([])),f.isTrue(t.size()<=1),this.coordinates=t},isSimple:function(){return!0},interfaces_:function(){return[wt]},getClass:function(){return Lt}}),Lt.serialVersionUID=0x44077bad161cbc00,e(Rt.prototype,{interfaces_:function(){return[]},getClass:function(){return Rt}}),h(Tt,B),e(Tt.prototype,{computeEnvelopeInternal:function(){return this.shell.getEnvelopeInternal()},getSortIndex:function(){return B.SORTINDEX_POLYGON},getCoordinates:function(){if(this.isEmpty())return[];for(var t=new Array(this.getNumPoints()).fill(null),e=-1,n=this.shell.getCoordinates(),i=0;i<n.length;i++)e++,t[e]=n[i];for(var r=0;r<this.holes.length;r++)for(var s=this.holes[r].getCoordinates(),o=0;o<s.length;o++)e++,t[e]=s[o];return t},getArea:function(){var t=0;t+=Math.abs(he.signedArea(this.shell.getCoordinateSequence()));for(var e=0;e<this.holes.length;e++)t-=Math.abs(he.signedArea(this.holes[e].getCoordinateSequence()));return t},isRectangle:function(){if(0!==this.getNumInteriorRing())return!1;if(null===this.shell)return!1;if(5!==this.shell.getNumPoints())return!1;for(var t=this.shell.getCoordinateSequence(),e=this.getEnvelopeInternal(),n=0;5>n;n++){var i=t.getX(n);if(i!==e.getMinX()&&i!==e.getMaxX())return!1;var r=t.getY(n);if(r!==e.getMinY()&&r!==e.getMaxY())return!1}for(var s=t.getX(0),o=t.getY(0),n=1;4>=n;n++){var i=t.getX(n),r=t.getY(n),a=i!==s,u=r!==o;if(a===u)return!1;s=i,o=r}return!0},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(!this.isEquivalentClass(t))return!1;var n=t,i=this.shell,r=n.shell;if(!i.equalsExact(r,e))return!1;if(this.holes.length!==n.holes.length)return!1;for(var s=0;s<this.holes.length;s++)if(!this.holes[s].equalsExact(n.holes[s],e))return!1;return!0}return B.prototype.equalsExact.apply(this,arguments)},normalize:function(){if(0===arguments.length){this.normalize(this.shell,!0);for(var t=0;t<this.holes.length;t++)this.normalize(this.holes[t],!1);ut.sort(this.holes)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(e.isEmpty())return null;var i=new Array(e.getCoordinates().length-1).fill(null);A.arraycopy(e.getCoordinates(),0,i,0,i.length);var r=H.minCoordinate(e.getCoordinates());H.scroll(i,r),A.arraycopy(i,0,e.getCoordinates(),0,i.length),e.getCoordinates()[i.length]=i[0],he.isCCW(e.getCoordinates())===n&&H.reverse(e.getCoordinates())}},getCoordinate:function(){return this.shell.getCoordinate()},getNumInteriorRing:function(){return this.holes.length},getBoundaryDimension:function(){return 1},getDimension:function(){return 2},getLength:function(){var t=0;t+=this.shell.getLength();for(var e=0;e<this.holes.length;e++)t+=this.holes[e].getLength();return t},getNumPoints:function(){for(var t=this.shell.getNumPoints(),e=0;e<this.holes.length;e++)t+=this.holes[e].getNumPoints();return t},reverse:function(){var t=this.copy();t.shell=this.shell.copy().reverse(),t.holes=new Array(this.holes.length).fill(null);for(var e=0;e<this.holes.length;e++)t.holes[e]=this.holes[e].copy().reverse();return t},convexHull:function(){return this.getExteriorRing().convexHull()},compareToSameClass:function(){if(1===arguments.length){var t=arguments[0],e=this.shell,n=t.shell;return e.compareToSameClass(n)}if(2===arguments.length){var i=arguments[0],r=arguments[1],s=i,e=this.shell,n=s.shell,o=e.compareToSameClass(n,r);if(0!==o)return o;for(var a=this.getNumInteriorRing(),u=s.getNumInteriorRing(),l=0;a>l&&u>l;){var h=this.getInteriorRingN(l),c=s.getInteriorRingN(l),f=h.compareToSameClass(c,r);if(0!==f)return f;l++}return a>l?1:u>l?-1:0}},apply:function(){if(R(arguments[0],z)){var t=arguments[0];this.shell.apply(t);for(var e=0;e<this.holes.length;e++)this.holes[e].apply(t)}else if(R(arguments[0],ct)){var n=arguments[0];if(this.shell.apply(n),!n.isDone())for(var e=0;e<this.holes.length&&(this.holes[e].apply(n),!n.isDone());e++);n.isGeometryChanged()&&this.geometryChanged()}else if(R(arguments[0],ht)){var i=arguments[0];i.filter(this)}else if(R(arguments[0],q)){var r=arguments[0];r.filter(this),this.shell.apply(r);for(var e=0;e<this.holes.length;e++)this.holes[e].apply(r)}},getBoundary:function(){if(this.isEmpty())return this.getFactory().createMultiLineString();var t=new Array(this.holes.length+1).fill(null);t[0]=this.shell;for(var e=0;e<this.holes.length;e++)t[e+1]=this.holes[e];return t.length<=1?this.getFactory().createLinearRing(t[0].getCoordinateSequence()):this.getFactory().createMultiLineString(t)},clone:function(){var t=B.prototype.clone.call(this);t.shell=this.shell.clone(),t.holes=new Array(this.holes.length).fill(null);for(var e=0;e<this.holes.length;e++)t.holes[e]=this.holes[e].clone();return t},getGeometryType:function(){return"Polygon"},copy:function(){for(var t=this.shell.copy(),e=new Array(this.holes.length).fill(null),n=0;n<e.length;n++)e[n]=this.holes[n].copy();return new Tt(t,e,this.factory)},getExteriorRing:function(){return this.shell},isEmpty:function(){return this.shell.isEmpty()},getInteriorRingN:function(t){return this.holes[t]},interfaces_:function(){return[Rt]},getClass:function(){return Tt}}),Tt.serialVersionUID=-0x307ffefd8dc97200,h(Pt,ft),e(Pt.prototype,{getSortIndex:function(){return B.SORTINDEX_MULTIPOINT},isValid:function(){return!0},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.isEquivalentClass(t)?ft.prototype.equalsExact.call(this,t,e):!1}return ft.prototype.equalsExact.apply(this,arguments)},getCoordinate:function(){if(1===arguments.length){var t=arguments[0];return this.geometries[t].getCoordinate()}return ft.prototype.getCoordinate.apply(this,arguments)},getBoundaryDimension:function(){return lt.FALSE},getDimension:function(){return 0},getBoundary:function(){return this.getFactory().createGeometryCollection(null)},getGeometryType:function(){return"MultiPoint"},copy:function(){for(var t=new Array(this.geometries.length).fill(null),e=0;e<t.length;e++)t[e]=this.geometries[e].copy();return new Pt(t,this.factory)},interfaces_:function(){return[wt]},getClass:function(){return Pt}}),Pt.serialVersionUID=-0x6fb1ed4162e0fc00,h(bt,St),e(bt.prototype,{getSortIndex:function(){return B.SORTINDEX_LINEARRING},getBoundaryDimension:function(){return lt.FALSE},isClosed:function(){return this.isEmpty()?!0:St.prototype.isClosed.call(this)},reverse:function(){var t=this.points.copy();Ct.reverse(t);var e=this.getFactory().createLinearRing(t);return e},validateConstruction:function(){if(!this.isEmpty()&&!St.prototype.isClosed.call(this))throw new i("Points of LinearRing do not form a closed linestring");if(this.getCoordinateSequence().size()>=1&&this.getCoordinateSequence().size()<bt.MINIMUM_VALID_SIZE)throw new i("Invalid number of points in LinearRing (found "+this.getCoordinateSequence().size()+" - must be 0 or >= 4)")},getGeometryType:function(){return"LinearRing"},copy:function(){return new bt(this.points.copy(),this.factory)},interfaces_:function(){return[]},getClass:function(){return bt}}),bt.MINIMUM_VALID_SIZE=4,bt.serialVersionUID=-0x3b229e262367a600,h(Ot,ft),e(Ot.prototype,{getSortIndex:function(){return B.SORTINDEX_MULTIPOLYGON},equalsExact:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.isEquivalentClass(t)?ft.prototype.equalsExact.call(this,t,e):!1}return ft.prototype.equalsExact.apply(this,arguments)},getBoundaryDimension:function(){return 1},getDimension:function(){return 2},reverse:function(){for(var t=this.geometries.length,e=new Array(t).fill(null),n=0;n<this.geometries.length;n++)e[n]=this.geometries[n].reverse();return this.getFactory().createMultiPolygon(e)},getBoundary:function(){if(this.isEmpty())return this.getFactory().createMultiLineString();for(var t=new I,e=0;e<this.geometries.length;e++)for(var n=this.geometries[e],i=n.getBoundary(),r=0;r<i.getNumGeometries();r++)t.add(i.getGeometryN(r));var s=new Array(t.size()).fill(null);return this.getFactory().createMultiLineString(t.toArray(s))},getGeometryType:function(){return"MultiPolygon"},copy:function(){for(var t=new Array(this.geometries.length).fill(null),e=0;e<t.length;e++)t[e]=this.geometries[e].copy();return new Ot(t,this.factory)},interfaces_:function(){return[Rt]},getClass:function(){return Ot}}),Ot.serialVersionUID=-0x7a5aa1369171980,e(_t.prototype,{setCopyUserData:function(t){this.isUserDataCopied=t},edit:function(t,e){if(null===t)return null;var n=this.editInternal(t,e);return this.isUserDataCopied&&n.setUserData(t.getUserData()),n},editInternal:function(t,e){return null===this.factory&&(this.factory=t.getFactory()),t instanceof ft?this.editGeometryCollection(t,e):t instanceof Tt?this.editPolygon(t,e):t instanceof Lt?e.edit(t,this.factory):t instanceof St?e.edit(t,this.factory):(f.shouldNeverReachHere("Unsupported Geometry class: "+t.getClass().getName()),null)},editGeometryCollection:function(t,e){for(var n=e.edit(t,this.factory),i=new I,r=0;r<n.getNumGeometries();r++){var s=this.edit(n.getGeometryN(r),e);null===s||s.isEmpty()||i.add(s)}return n.getClass()===Pt?this.factory.createMultiPoint(i.toArray([])):n.getClass()===gt?this.factory.createMultiLineString(i.toArray([])):n.getClass()===Ot?this.factory.createMultiPolygon(i.toArray([])):this.factory.createGeometryCollection(i.toArray([]))},editPolygon:function(t,e){var n=e.edit(t,this.factory);if(null===n&&(n=this.factory.createPolygon(null)),n.isEmpty())return n;var i=this.edit(n.getExteriorRing(),e);if(null===i||i.isEmpty())return this.factory.createPolygon();for(var r=new I,s=0;s<n.getNumInteriorRing();s++){var o=this.edit(n.getInteriorRingN(s),e);null===o||o.isEmpty()||r.add(o)}return this.factory.createPolygon(i,r.toArray([]))},interfaces_:function(){return[]},getClass:function(){return _t}}),_t.GeometryEditorOperation=Mt,e(Dt.prototype,{edit:function(t,e){return t},interfaces_:function(){return[Mt]},getClass:function(){return Dt}}),e(At.prototype,{edit:function(t,e){if(t instanceof bt)return e.createLinearRing(this.editCoordinates(t.getCoordinates(),t));if(t instanceof St)return e.createLineString(this.editCoordinates(t.getCoordinates(),t));if(t instanceof Lt){var n=this.editCoordinates(t.getCoordinates(),t);return n.length>0?e.createPoint(n[0]):e.createPoint()}return t},interfaces_:function(){return[Mt]},getClass:function(){return At}}),e(Ft.prototype,{edit:function(t,e){return t instanceof bt?e.createLinearRing(this.edit(t.getCoordinateSequence(),t)):t instanceof St?e.createLineString(this.edit(t.getCoordinateSequence(),t)):t instanceof Lt?e.createPoint(this.edit(t.getCoordinateSequence(),t)):t},interfaces_:function(){return[Mt]},getClass:function(){return Ft}}),_t.NoOpGeometryOperation=Dt,_t.CoordinateOperation=At,_t.CoordinateSequenceOperation=Ft,e(Gt.prototype,{setOrdinate:function(t,e,n){switch(e){case D.X:this.coordinates[t].x=n;break;case D.Y:this.coordinates[t].y=n;break;case D.Z:this.coordinates[t].z=n;break;default:throw new i("invalid ordinateIndex")}},size:function(){return this.coordinates.length},getOrdinate:function(t,e){switch(e){case D.X:return this.coordinates[t].x;case D.Y:return this.coordinates[t].y;case D.Z:return this.coordinates[t].z}return r.NaN},getCoordinate:function(){if(1===arguments.length){var t=arguments[0];return this.coordinates[t]}if(2===arguments.length){var e=arguments[0],n=arguments[1];n.x=this.coordinates[e].x,n.y=this.coordinates[e].y,n.z=this.coordinates[e].z}},getCoordinateCopy:function(t){return new g(this.coordinates[t])},getDimension:function(){return this.dimension},getX:function(t){return this.coordinates[t].x},clone:function(){for(var t=new Array(this.size()).fill(null),e=0;e<this.coordinates.length;e++)t[e]=this.coordinates[e].clone();return new Gt(t,this.dimension)},expandEnvelope:function(t){for(var e=0;e<this.coordinates.length;e++)t.expandToInclude(this.coordinates[e]);return t},copy:function(){for(var t=new Array(this.size()).fill(null),e=0;e<this.coordinates.length;e++)t[e]=this.coordinates[e].copy();return new Gt(t,this.dimension)},toString:function(){if(this.coordinates.length>0){var t=new P(17*this.coordinates.length);t.append("("),t.append(this.coordinates[0]);for(var e=1;e<this.coordinates.length;e++)t.append(", "),t.append(this.coordinates[e]);return t.append(")"),t.toString()}return"()"},getY:function(t){return this.coordinates[t].y},toCoordinateArray:function(){return this.coordinates},interfaces_:function(){return[D,u]},getClass:function(){return Gt}}),Gt.serialVersionUID=-0xcb44a778db18e00,e(qt.prototype,{readResolve:function(){return qt.instance()},create:function(){if(1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];return new Gt(t)}if(R(arguments[0],D)){var e=arguments[0];return new Gt(e)}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];return i>3&&(i=3),2>i?new Gt(n):new Gt(n,i)}},interfaces_:function(){return[G,u]},getClass:function(){return qt}}),qt.instance=function(){return qt.instanceObject},qt.serialVersionUID=-0x38e49fa6cf6f2e00,qt.instanceObject=new qt;var no,io=Object.defineProperty,ro=zt({"delete":kt,has:Xt,get:Yt,set:Ht,keys:jt,values:Kt,entries:Zt,forEach:$t,clear:Wt}),so="undefined"!=typeof Map&&Map.prototype.values?Map:ro;te.prototype=new K,te.prototype.get=function(t){return this.map_.get(t)||null},te.prototype.put=function(t,e){return this.map_.set(t,e),e},te.prototype.values=function(){for(var t=new I,e=this.map_.values(),n=e.next();!n.done;)t.add(n.value),n=e.next();return t},te.prototype.entrySet=function(){var t=new J;return this.map_.entries().forEach(function(e){return t.add(e)}),t},te.prototype.size=function(){return this.map_.size()},e(ee.prototype,{equals:function(t){if(!(t instanceof ee))return!1;var e=t;return this.modelType===e.modelType&&this.scale===e.scale},compareTo:function(t){var e=t,n=this.getMaximumSignificantDigits(),i=e.getMaximumSignificantDigits();return new b(n).compareTo(new b(i))},getScale:function(){return this.scale},isFloating:function(){return this.modelType===ee.FLOATING||this.modelType===ee.FLOATING_SINGLE},getType:function(){return this.modelType},toString:function(){var t="UNKNOWN";return this.modelType===ee.FLOATING?t="Floating":this.modelType===ee.FLOATING_SINGLE?t="Floating-Single":this.modelType===ee.FIXED&&(t="Fixed (Scale="+this.getScale()+")"),t},makePrecise:function(){if("number"==typeof arguments[0]){var t=arguments[0];if(r.isNaN(t))return t;if(this.modelType===ee.FLOATING_SINGLE){var e=t;return e}return this.modelType===ee.FIXED?Math.round(t*this.scale)/this.scale:t}if(arguments[0]instanceof g){var n=arguments[0];if(this.modelType===ee.FLOATING)return null;n.x=this.makePrecise(n.x),n.y=this.makePrecise(n.y)}},getMaximumSignificantDigits:function(){var t=16;return this.modelType===ee.FLOATING?t=16:this.modelType===ee.FLOATING_SINGLE?t=6:this.modelType===ee.FIXED&&(t=1+Math.trunc(Math.ceil(Math.log(this.getScale())/Math.log(10)))),t},setScale:function(t){this.scale=Math.abs(t)},interfaces_:function(){return[u,s]},getClass:function(){return ee}}),ee.mostPrecise=function(t,e){return t.compareTo(e)>=0?t:e},e(ne.prototype,{readResolve:function(){return ne.nameToTypeMap.get(this.name)},toString:function(){return this.name},interfaces_:function(){return[u]},getClass:function(){return ne}}),ne.serialVersionUID=-552860263173159e4,ne.nameToTypeMap=new te,ee.Type=ne,ee.serialVersionUID=0x6bee6404e9a25c00,ee.FIXED=new ne("FIXED"),ee.FLOATING=new ne("FLOATING"),ee.FLOATING_SINGLE=new ne("FLOATING SINGLE"),ee.maximumPreciseValue=9007199254740992,e(ie.prototype,{toGeometry:function(t){return t.isNull()?this.createPoint(null):t.getMinX()===t.getMaxX()&&t.getMinY()===t.getMaxY()?this.createPoint(new g(t.getMinX(),t.getMinY())):t.getMinX()===t.getMaxX()||t.getMinY()===t.getMaxY()?this.createLineString([new g(t.getMinX(),t.getMinY()),new g(t.getMaxX(),t.getMaxY())]):this.createPolygon(this.createLinearRing([new g(t.getMinX(),t.getMinY()),new g(t.getMinX(),t.getMaxY()),new g(t.getMaxX(),t.getMaxY()),new g(t.getMaxX(),t.getMinY()),new g(t.getMinX(),t.getMinY())]),null)},createLineString:function(){if(0===arguments.length)return this.createLineString(this.getCoordinateSequenceFactory().create([]));if(1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];return this.createLineString(null!==t?this.getCoordinateSequenceFactory().create(t):null)}if(R(arguments[0],D)){var e=arguments[0];return new St(e,this)}}},createMultiLineString:function(){if(0===arguments.length)return new gt(null,this);if(1===arguments.length){var t=arguments[0];return new gt(t,this)}},buildGeometry:function(t){for(var e=null,n=!1,i=!1,r=t.iterator();r.hasNext();){var s=r.next(),o=s.getClass();null===e&&(e=o),o!==e&&(n=!0),s.isGeometryCollectionOrDerived()&&(i=!0)}if(null===e)return this.createGeometryCollection();if(n||i)return this.createGeometryCollection(ie.toGeometryArray(t));var a=t.iterator().next(),u=t.size()>1;if(u){if(a instanceof Tt)return this.createMultiPolygon(ie.toPolygonArray(t));if(a instanceof St)return this.createMultiLineString(ie.toLineStringArray(t));if(a instanceof Lt)return this.createMultiPoint(ie.toPointArray(t));f.shouldNeverReachHere("Unhandled class: "+a.getClass().getName())}return a},createMultiPointFromCoords:function(t){return this.createMultiPoint(null!==t?this.getCoordinateSequenceFactory().create(t):null)},createPoint:function(){if(0===arguments.length)return this.createPoint(this.getCoordinateSequenceFactory().create([]));if(1===arguments.length){if(arguments[0]instanceof g){var t=arguments[0];return this.createPoint(null!==t?this.getCoordinateSequenceFactory().create([t]):null)}if(R(arguments[0],D)){var e=arguments[0];return new Lt(e,this)}}},getCoordinateSequenceFactory:function(){return this.coordinateSequenceFactory},createPolygon:function(){if(0===arguments.length)return new Tt(null,null,this);if(1===arguments.length){if(R(arguments[0],D)){var t=arguments[0];return this.createPolygon(this.createLinearRing(t))}if(arguments[0]instanceof Array){var e=arguments[0];return this.createPolygon(this.createLinearRing(e))}if(arguments[0]instanceof bt){var n=arguments[0];return this.createPolygon(n,null)}}else if(2===arguments.length){var i=arguments[0],r=arguments[1];return new Tt(i,r,this)}},getSRID:function(){return this.SRID},createGeometryCollection:function(){if(0===arguments.length)return new ft(null,this);if(1===arguments.length){var t=arguments[0];return new ft(t,this)}},createGeometry:function(t){var e=new _t(this);return e.edit(t,{edit:function(){if(2===arguments.length){var t=arguments[0];arguments[1];return this.coordinateSequenceFactory.create(t)}}})},getPrecisionModel:function(){return this.precisionModel},createLinearRing:function(){if(0===arguments.length)return this.createLinearRing(this.getCoordinateSequenceFactory().create([]));if(1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];return this.createLinearRing(null!==t?this.getCoordinateSequenceFactory().create(t):null)}if(R(arguments[0],D)){var e=arguments[0];return new bt(e,this)}}},createMultiPolygon:function(){if(0===arguments.length)return new Ot(null,this);if(1===arguments.length){var t=arguments[0];return new Ot(t,this)}},createMultiPoint:function(){if(0===arguments.length)return new Pt(null,this);if(1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];return new Pt(t,this)}if(arguments[0]instanceof Array){var e=arguments[0];return this.createMultiPoint(null!==e?this.getCoordinateSequenceFactory().create(e):null)}if(R(arguments[0],D)){var n=arguments[0];if(null===n)return this.createMultiPoint(new Array(0).fill(null));for(var i=new Array(n.size()).fill(null),r=0;r<n.size();r++){var s=this.getCoordinateSequenceFactory().create(1,n.getDimension());Ct.copy(n,r,s,0,1),i[r]=this.createPoint(s)}return this.createMultiPoint(i)}}},interfaces_:function(){return[u]},getClass:function(){return ie}}),ie.toMultiPolygonArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toGeometryArray=function(t){if(null===t)return null;var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.getDefaultCoordinateSequenceFactory=function(){return qt.instance()},ie.toMultiLineStringArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toLineStringArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toMultiPointArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toLinearRingArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toPointArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.toPolygonArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},ie.createPointFromInternalCoord=function(t,e){return e.getPrecisionModel().makePrecise(t),e.getFactory().createPoint(t)},ie.serialVersionUID=-0x5ea75f2051eeb400;var oo={typeStr:/^\s*(\w+)\s*\(\s*(.*)\s*\)\s*$/,emptyTypeStr:/^\s*(\w+)\s*EMPTY\s*$/,spaces:/\s+/,parenComma:/\)\s*,\s*\(/,doubleParenComma:/\)\s*\)\s*,\s*\(\s*\(/,trimParens:/^\s*\(?(.*?)\)?\s*$/};e(re.prototype,{read:function(t){var e,n,i;t=t.replace(/[\n\r]/g," ");var r=oo.typeStr.exec(t);if(-1!==t.search("EMPTY")&&(r=oo.emptyTypeStr.exec(t),r[2]=void 0),r&&(n=r[1].toLowerCase(),i=r[2],uo[n]&&(e=uo[n].apply(this,[i]))),void 0===e)throw new Error("Could not parse WKT "+t);return e},write:function(t){return this.extractGeometry(t);
},extractGeometry:function(t){var e=t.getGeometryType().toLowerCase();if(!ao[e])return null;var n,i=e.toUpperCase();return n=t.isEmpty()?i+" EMPTY":i+"("+ao[e].apply(this,[t])+")"}});var ao={coordinate:function(t){return t.x+" "+t.y},point:function(t){return ao.coordinate.call(this,t.coordinates.coordinates[0])},multipoint:function(t){for(var e=[],n=0,i=t.geometries.length;i>n;++n)e.push("("+ao.point.apply(this,[t.geometries[n]])+")");return e.join(",")},linestring:function(t){for(var e=[],n=0,i=t.points.coordinates.length;i>n;++n)e.push(ao.coordinate.apply(this,[t.points.coordinates[n]]));return e.join(",")},linearring:function(t){for(var e=[],n=0,i=t.points.coordinates.length;i>n;++n)e.push(ao.coordinate.apply(this,[t.points.coordinates[n]]));return e.join(",")},multilinestring:function(t){for(var e=[],n=0,i=t.geometries.length;i>n;++n)e.push("("+ao.linestring.apply(this,[t.geometries[n]])+")");return e.join(",")},polygon:function(t){var e=[];e.push("("+ao.linestring.apply(this,[t.shell])+")");for(var n=0,i=t.holes.length;i>n;++n)e.push("("+ao.linestring.apply(this,[t.holes[n]])+")");return e.join(",")},multipolygon:function(t){for(var e=[],n=0,i=t.geometries.length;i>n;++n)e.push("("+ao.polygon.apply(this,[t.geometries[n]])+")");return e.join(",")},geometrycollection:function(t){for(var e=[],n=0,i=t.geometries.length;i>n;++n)e.push(this.extractGeometry(t.geometries[n]));return e.join(",")}},uo={point:function(t){if(void 0===t)return this.geometryFactory.createPoint();var e=t.trim().split(oo.spaces);return this.geometryFactory.createPoint(new g(Number.parseFloat(e[0]),Number.parseFloat(e[1])))},multipoint:function(t){if(void 0===t)return this.geometryFactory.createMultiPoint();for(var e,n=t.trim().split(","),i=[],r=0,s=n.length;s>r;++r)e=n[r].replace(oo.trimParens,"$1"),i.push(uo.point.apply(this,[e]));return this.geometryFactory.createMultiPoint(i)},linestring:function(t){if(void 0===t)return this.geometryFactory.createLineString();for(var e,n=t.trim().split(","),i=[],r=0,s=n.length;s>r;++r)e=n[r].trim().split(oo.spaces),i.push(new g(Number.parseFloat(e[0]),Number.parseFloat(e[1])));return this.geometryFactory.createLineString(i)},linearring:function(t){if(void 0===t)return this.geometryFactory.createLinearRing();for(var e,n=t.trim().split(","),i=[],r=0,s=n.length;s>r;++r)e=n[r].trim().split(oo.spaces),i.push(new g(Number.parseFloat(e[0]),Number.parseFloat(e[1])));return this.geometryFactory.createLinearRing(i)},multilinestring:function(t){if(void 0===t)return this.geometryFactory.createMultiLineString();for(var e,n=t.trim().split(oo.parenComma),i=[],r=0,s=n.length;s>r;++r)e=n[r].replace(oo.trimParens,"$1"),i.push(uo.linestring.apply(this,[e]));return this.geometryFactory.createMultiLineString(i)},polygon:function(t){if(void 0===t)return this.geometryFactory.createPolygon();for(var e,n,i,r,s=t.trim().split(oo.parenComma),o=[],a=0,u=s.length;u>a;++a)e=s[a].replace(oo.trimParens,"$1"),n=uo.linestring.apply(this,[e]),i=this.geometryFactory.createLinearRing(n.points),0===a?r=i:o.push(i);return this.geometryFactory.createPolygon(r,o)},multipolygon:function(t){if(void 0===t)return this.geometryFactory.createMultiPolygon();for(var e,n=t.trim().split(oo.doubleParenComma),i=[],r=0,s=n.length;s>r;++r)e=n[r].replace(oo.trimParens,"$1"),i.push(uo.polygon.apply(this,[e]));return this.geometryFactory.createMultiPolygon(i)},geometrycollection:function(t){if(void 0===t)return this.geometryFactory.createGeometryCollection();t=t.replace(/,\s*([A-Za-z])/g,"|$1");for(var e=t.trim().split("|"),n=[],i=0,r=e.length;r>i;++i)n.push(this.read(e[i]));return this.geometryFactory.createGeometryCollection(n)}};e(se.prototype,{write:function(t){return this.parser.write(t)}}),e(se,{toLineString:function(t,e){if(2!==arguments.length)throw new Error("Not implemented");return"LINESTRING ( "+t.x+" "+t.y+", "+e.x+" "+e.y+" )"}}),e(oe.prototype,{getIndexAlongSegment:function(t,e){return this.computeIntLineIndex(),this.intLineIndex[t][e]},getTopologySummary:function(){var t=new P;return this.isEndPoint()&&t.append(" endpoint"),this._isProper&&t.append(" proper"),this.isCollinear()&&t.append(" collinear"),t.toString()},computeIntersection:function(t,e,n,i){this.inputLines[0][0]=t,this.inputLines[0][1]=e,this.inputLines[1][0]=n,this.inputLines[1][1]=i,this.result=this.computeIntersect(t,e,n,i)},getIntersectionNum:function(){return this.result},computeIntLineIndex:function(){if(0===arguments.length)null===this.intLineIndex&&(this.intLineIndex=Array(2).fill().map(function(){return Array(2)}),this.computeIntLineIndex(0),this.computeIntLineIndex(1));else if(1===arguments.length){var t=arguments[0],e=this.getEdgeDistance(t,0),n=this.getEdgeDistance(t,1);e>n?(this.intLineIndex[t][0]=0,this.intLineIndex[t][1]=1):(this.intLineIndex[t][0]=1,this.intLineIndex[t][1]=0)}},isProper:function(){return this.hasIntersection()&&this._isProper},setPrecisionModel:function(t){this.precisionModel=t},isInteriorIntersection:function(){if(0===arguments.length)return this.isInteriorIntersection(0)?!0:!!this.isInteriorIntersection(1);if(1===arguments.length){for(var t=arguments[0],e=0;e<this.result;e++)if(!this.intPt[e].equals2D(this.inputLines[t][0])&&!this.intPt[e].equals2D(this.inputLines[t][1]))return!0;return!1}},getIntersection:function(t){return this.intPt[t]},isEndPoint:function(){return this.hasIntersection()&&!this._isProper},hasIntersection:function(){return this.result!==oe.NO_INTERSECTION},getEdgeDistance:function(t,e){var n=oe.computeEdgeDistance(this.intPt[e],this.inputLines[t][0],this.inputLines[t][1]);return n},isCollinear:function(){return this.result===oe.COLLINEAR_INTERSECTION},toString:function(){return se.toLineString(this.inputLines[0][0],this.inputLines[0][1])+" - "+se.toLineString(this.inputLines[1][0],this.inputLines[1][1])+this.getTopologySummary()},getEndpoint:function(t,e){return this.inputLines[t][e]},isIntersection:function(t){for(var e=0;e<this.result;e++)if(this.intPt[e].equals2D(t))return!0;return!1},getIntersectionAlongSegment:function(t,e){return this.computeIntLineIndex(),this.intPt[this.intLineIndex[t][e]]},interfaces_:function(){return[]},getClass:function(){return oe}}),oe.computeEdgeDistance=function(t,e,n){var i=Math.abs(n.x-e.x),r=Math.abs(n.y-e.y),s=-1;if(t.equals(e))s=0;else if(t.equals(n))s=i>r?i:r;else{var o=Math.abs(t.x-e.x),a=Math.abs(t.y-e.y);s=i>r?o:a,0!==s||t.equals(e)||(s=Math.max(o,a))}return f.isTrue(!(0===s&&!t.equals(e)),"Bad distance calculation"),s},oe.nonRobustComputeEdgeDistance=function(t,e,n){var i=t.x-e.x,r=t.y-e.y,s=Math.sqrt(i*i+r*r);return f.isTrue(!(0===s&&!t.equals(e)),"Invalid distance calculation"),s},oe.DONT_INTERSECT=0,oe.DO_INTERSECT=1,oe.COLLINEAR=2,oe.NO_INTERSECTION=0,oe.POINT_INTERSECTION=1,oe.COLLINEAR_INTERSECTION=2,h(ae,oe),e(ae.prototype,{isInSegmentEnvelopes:function(t){var e=new C(this.inputLines[0][0],this.inputLines[0][1]),n=new C(this.inputLines[1][0],this.inputLines[1][1]);return e.contains(t)&&n.contains(t)},computeIntersection:function(){if(3!==arguments.length)return oe.prototype.computeIntersection.apply(this,arguments);var t=arguments[0],e=arguments[1],n=arguments[2];return this._isProper=!1,C.intersects(e,n,t)&&0===he.orientationIndex(e,n,t)&&0===he.orientationIndex(n,e,t)?(this._isProper=!0,(t.equals(e)||t.equals(n))&&(this._isProper=!1),this.result=oe.POINT_INTERSECTION,null):void(this.result=oe.NO_INTERSECTION)},normalizeToMinimum:function(t,e,n,i,r){r.x=this.smallestInAbsValue(t.x,e.x,n.x,i.x),r.y=this.smallestInAbsValue(t.y,e.y,n.y,i.y),t.x-=r.x,t.y-=r.y,e.x-=r.x,e.y-=r.y,n.x-=r.x,n.y-=r.y,i.x-=r.x,i.y-=r.y},safeHCoordinateIntersection:function(t,e,n,i){var r=null;try{r=F.intersection(t,e,n,i)}catch(s){if(!(s instanceof w))throw s;r=ae.nearestEndpoint(t,e,n,i)}finally{}return r},intersection:function(t,e,n,i){var r=this.intersectionWithNormalization(t,e,n,i);return this.isInSegmentEnvelopes(r)||(r=new g(ae.nearestEndpoint(t,e,n,i))),null!==this.precisionModel&&this.precisionModel.makePrecise(r),r},smallestInAbsValue:function(t,e,n,i){var r=t,s=Math.abs(r);return Math.abs(e)<s&&(r=e,s=Math.abs(e)),Math.abs(n)<s&&(r=n,s=Math.abs(n)),Math.abs(i)<s&&(r=i),r},checkDD:function(t,e,n,i,r){var s=M.intersection(t,e,n,i),o=this.isInSegmentEnvelopes(s);A.out.println("DD in env = "+o+"  --------------------- "+s),r.distance(s)>1e-4&&A.out.println("Distance = "+r.distance(s))},intersectionWithNormalization:function(t,e,n,i){var r=new g(t),s=new g(e),o=new g(n),a=new g(i),u=new g;this.normalizeToEnvCentre(r,s,o,a,u);var l=this.safeHCoordinateIntersection(r,s,o,a);return l.x+=u.x,l.y+=u.y,l},computeCollinearIntersection:function(t,e,n,i){var r=C.intersects(t,e,n),s=C.intersects(t,e,i),o=C.intersects(n,i,t),a=C.intersects(n,i,e);return r&&s?(this.intPt[0]=n,this.intPt[1]=i,oe.COLLINEAR_INTERSECTION):o&&a?(this.intPt[0]=t,this.intPt[1]=e,oe.COLLINEAR_INTERSECTION):r&&o?(this.intPt[0]=n,this.intPt[1]=t,!n.equals(t)||s||a?oe.COLLINEAR_INTERSECTION:oe.POINT_INTERSECTION):r&&a?(this.intPt[0]=n,this.intPt[1]=e,!n.equals(e)||s||o?oe.COLLINEAR_INTERSECTION:oe.POINT_INTERSECTION):s&&o?(this.intPt[0]=i,this.intPt[1]=t,!i.equals(t)||r||a?oe.COLLINEAR_INTERSECTION:oe.POINT_INTERSECTION):s&&a?(this.intPt[0]=i,this.intPt[1]=e,!i.equals(e)||r||o?oe.COLLINEAR_INTERSECTION:oe.POINT_INTERSECTION):oe.NO_INTERSECTION},normalizeToEnvCentre:function(t,e,n,i,r){var s=t.x<e.x?t.x:e.x,o=t.y<e.y?t.y:e.y,a=t.x>e.x?t.x:e.x,u=t.y>e.y?t.y:e.y,l=n.x<i.x?n.x:i.x,h=n.y<i.y?n.y:i.y,c=n.x>i.x?n.x:i.x,f=n.y>i.y?n.y:i.y,g=s>l?s:l,d=c>a?a:c,p=o>h?o:h,v=f>u?u:f,m=(g+d)/2,y=(p+v)/2;r.x=m,r.y=y,t.x-=r.x,t.y-=r.y,e.x-=r.x,e.y-=r.y,n.x-=r.x,n.y-=r.y,i.x-=r.x,i.y-=r.y},computeIntersect:function(t,e,n,i){if(this._isProper=!1,!C.intersects(t,e,n,i))return oe.NO_INTERSECTION;var r=he.orientationIndex(t,e,n),s=he.orientationIndex(t,e,i);if(r>0&&s>0||0>r&&0>s)return oe.NO_INTERSECTION;var o=he.orientationIndex(n,i,t),a=he.orientationIndex(n,i,e);if(o>0&&a>0||0>o&&0>a)return oe.NO_INTERSECTION;var u=0===r&&0===s&&0===o&&0===a;return u?this.computeCollinearIntersection(t,e,n,i):(0===r||0===s||0===o||0===a?(this._isProper=!1,t.equals2D(n)||t.equals2D(i)?this.intPt[0]=t:e.equals2D(n)||e.equals2D(i)?this.intPt[0]=e:0===r?this.intPt[0]=new g(n):0===s?this.intPt[0]=new g(i):0===o?this.intPt[0]=new g(t):0===a&&(this.intPt[0]=new g(e))):(this._isProper=!0,this.intPt[0]=this.intersection(t,e,n,i)),oe.POINT_INTERSECTION)},interfaces_:function(){return[]},getClass:function(){return ae}}),ae.nearestEndpoint=function(t,e,n,i){var r=t,s=he.distancePointLine(t,n,i),o=he.distancePointLine(e,n,i);return s>o&&(s=o,r=e),o=he.distancePointLine(n,t,e),s>o&&(s=o,r=n),o=he.distancePointLine(i,t,e),s>o&&(s=o,r=i),r},e(ue.prototype,{interfaces_:function(){return[]},getClass:function(){return ue}}),ue.orientationIndex=function(t,e,n){var i=e.x-t.x,r=e.y-t.y,s=n.x-e.x,o=n.y-e.y;return ue.signOfDet2x2(i,r,s,o)},ue.signOfDet2x2=function(t,e,n,i){var r=null,s=null,o=null,a=0;if(r=1,0===t||0===i)return 0===e||0===n?0:e>0?n>0?-r:r:n>0?r:-r;if(0===e||0===n)return i>0?t>0?r:-r:t>0?-r:r;if(e>0?i>0?i>=e||(r=-r,s=t,t=n,n=s,s=e,e=i,i=s):-i>=e?(r=-r,n=-n,i=-i):(s=t,t=-n,n=s,s=e,e=-i,i=s):i>0?i>=-e?(r=-r,t=-t,e=-e):(s=-t,t=n,n=s,s=-e,e=i,i=s):e>=i?(t=-t,e=-e,n=-n,i=-i):(r=-r,s=-t,t=-n,n=s,s=-e,e=-i,i=s),t>0){if(!(n>0))return r;if(!(n>=t))return r}else{if(n>0)return-r;if(!(t>=n))return-r;r=-r,t=-t,n=-n}for(;;){if(a+=1,o=Math.floor(n/t),n-=o*t,i-=o*e,0>i)return-r;if(i>e)return r;if(t>n+n){if(i+i>e)return r}else{if(e>i+i)return-r;n=t-n,i=e-i,r=-r}if(0===i)return 0===n?0:-r;if(0===n)return r;if(o=Math.floor(t/n),t-=o*n,e-=o*i,0>e)return r;if(e>i)return-r;if(n>t+t){if(e+e>i)return-r}else{if(i>e+e)return r;t=n-t,e=i-e,r=-r}if(0===e)return 0===t?0:r;if(0===t)return-r}},e(le.prototype,{countSegment:function(t,e){if(t.x<this.p.x&&e.x<this.p.x)return null;if(this.p.x===e.x&&this.p.y===e.y)return this.isPointOnSegment=!0,null;if(t.y===this.p.y&&e.y===this.p.y){var n=t.x,i=e.x;return n>i&&(n=e.x,i=t.x),this.p.x>=n&&this.p.x<=i&&(this.isPointOnSegment=!0),null}if(t.y>this.p.y&&e.y<=this.p.y||e.y>this.p.y&&t.y<=this.p.y){var r=t.x-this.p.x,s=t.y-this.p.y,o=e.x-this.p.x,a=e.y-this.p.y,u=ue.signOfDet2x2(r,s,o,a);if(0===u)return this.isPointOnSegment=!0,null;s>a&&(u=-u),u>0&&this.crossingCount++}},isPointInPolygon:function(){return this.getLocation()!==L.EXTERIOR},getLocation:function(){return this.isPointOnSegment?L.BOUNDARY:this.crossingCount%2===1?L.INTERIOR:L.EXTERIOR},isOnSegment:function(){return this.isPointOnSegment},interfaces_:function(){return[]},getClass:function(){return le}}),le.locatePointInRing=function(){if(arguments[0]instanceof g&&R(arguments[1],D)){for(var t=arguments[0],e=arguments[1],n=new le(t),i=new g,r=new g,s=1;s<e.size();s++)if(e.getCoordinate(s,i),e.getCoordinate(s-1,r),n.countSegment(i,r),n.isOnSegment())return n.getLocation();return n.getLocation()}if(arguments[0]instanceof g&&arguments[1]instanceof Array){for(var o=arguments[0],a=arguments[1],n=new le(o),s=1;s<a.length;s++){var i=a[s],r=a[s-1];if(n.countSegment(i,r),n.isOnSegment())return n.getLocation()}return n.getLocation()}},e(he.prototype,{interfaces_:function(){return[]},getClass:function(){return he}}),he.orientationIndex=function(t,e,n){return M.orientationIndex(t,e,n)},he.signedArea=function(){if(arguments[0]instanceof Array){var t=arguments[0];if(t.length<3)return 0;for(var e=0,n=t[0].x,i=1;i<t.length-1;i++){var r=t[i].x-n,s=t[i+1].y,o=t[i-1].y;e+=r*(o-s)}return e/2}if(R(arguments[0],D)){var a=arguments[0],u=a.size();if(3>u)return 0;var l=new g,h=new g,c=new g;a.getCoordinate(0,h),a.getCoordinate(1,c);var n=h.x;c.x-=n;for(var e=0,i=1;u-1>i;i++)l.y=h.y,h.x=c.x,h.y=c.y,a.getCoordinate(i+1,c),c.x-=n,e+=h.x*(l.y-c.y);return e/2}},he.distanceLineLine=function(t,e,n,i){if(t.equals(e))return he.distancePointLine(t,n,i);if(n.equals(i))return he.distancePointLine(i,t,e);var r=!1;if(C.intersects(t,e,n,i)){var s=(e.x-t.x)*(i.y-n.y)-(e.y-t.y)*(i.x-n.x);if(0===s)r=!0;else{var o=(t.y-n.y)*(i.x-n.x)-(t.x-n.x)*(i.y-n.y),a=(t.y-n.y)*(e.x-t.x)-(t.x-n.x)*(e.y-t.y),u=a/s,l=o/s;(0>l||l>1||0>u||u>1)&&(r=!0)}}else r=!0;return r?T.min(he.distancePointLine(t,n,i),he.distancePointLine(e,n,i),he.distancePointLine(n,t,e),he.distancePointLine(i,t,e)):0},he.isPointInRing=function(t,e){return he.locatePointInRing(t,e)!==L.EXTERIOR},he.computeLength=function(t){var e=t.size();if(1>=e)return 0;var n=0,i=new g;t.getCoordinate(0,i);for(var r=i.x,s=i.y,o=1;e>o;o++){t.getCoordinate(o,i);var a=i.x,u=i.y,l=a-r,h=u-s;n+=Math.sqrt(l*l+h*h),r=a,s=u}return n},he.isCCW=function(t){var e=t.length-1;if(3>e)throw new i("Ring has fewer than 4 points, so orientation cannot be determined");for(var n=t[0],r=0,s=1;e>=s;s++){var o=t[s];o.y>n.y&&(n=o,r=s)}var a=r;do a-=1,0>a&&(a=e);while(t[a].equals2D(n)&&a!==r);var u=r;do u=(u+1)%e;while(t[u].equals2D(n)&&u!==r);var l=t[a],h=t[u];if(l.equals2D(n)||h.equals2D(n)||l.equals2D(h))return!1;var c=he.computeOrientation(l,n,h),f=!1;return f=0===c?l.x>h.x:c>0},he.locatePointInRing=function(t,e){return le.locatePointInRing(t,e)},he.distancePointLinePerpendicular=function(t,e,n){var i=(n.x-e.x)*(n.x-e.x)+(n.y-e.y)*(n.y-e.y),r=((e.y-t.y)*(n.x-e.x)-(e.x-t.x)*(n.y-e.y))/i;return Math.abs(r)*Math.sqrt(i)},he.computeOrientation=function(t,e,n){return he.orientationIndex(t,e,n)},he.distancePointLine=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(0===e.length)throw new i("Line array must contain at least one vertex");for(var n=t.distance(e[0]),r=0;r<e.length-1;r++){var s=he.distancePointLine(t,e[r],e[r+1]);n>s&&(n=s)}return n}if(3===arguments.length){var o=arguments[0],a=arguments[1],u=arguments[2];if(a.x===u.x&&a.y===u.y)return o.distance(a);var l=(u.x-a.x)*(u.x-a.x)+(u.y-a.y)*(u.y-a.y),h=((o.x-a.x)*(u.x-a.x)+(o.y-a.y)*(u.y-a.y))/l;if(0>=h)return o.distance(a);if(h>=1)return o.distance(u);var c=((a.y-o.y)*(u.x-a.x)-(a.x-o.x)*(u.y-a.y))/l;return Math.abs(c)*Math.sqrt(l)}},he.isOnLine=function(t,e){for(var n=new ae,i=1;i<e.length;i++){var r=e[i-1],s=e[i];if(n.computeIntersection(t,r,s),n.hasIntersection())return!0}return!1},he.CLOCKWISE=-1,he.RIGHT=he.CLOCKWISE,he.COUNTERCLOCKWISE=1,he.LEFT=he.COUNTERCLOCKWISE,he.COLLINEAR=0,he.STRAIGHT=he.COLLINEAR,e(ce.prototype,{minX:function(){return Math.min(this.p0.x,this.p1.x)},orientationIndex:function(){if(arguments[0]instanceof ce){var t=arguments[0],e=he.orientationIndex(this.p0,this.p1,t.p0),n=he.orientationIndex(this.p0,this.p1,t.p1);return e>=0&&n>=0?Math.max(e,n):0>=e&&0>=n?Math.max(e,n):0}if(arguments[0]instanceof g){var i=arguments[0];return he.orientationIndex(this.p0,this.p1,i)}},toGeometry:function(t){return t.createLineString([this.p0,this.p1])},isVertical:function(){return this.p0.x===this.p1.x},equals:function(t){if(!(t instanceof ce))return!1;var e=t;return this.p0.equals(e.p0)&&this.p1.equals(e.p1)},intersection:function(t){var e=new ae;return e.computeIntersection(this.p0,this.p1,t.p0,t.p1),e.hasIntersection()?e.getIntersection(0):null},project:function(){if(arguments[0]instanceof g){var t=arguments[0];if(t.equals(this.p0)||t.equals(this.p1))return new g(t);var e=this.projectionFactor(t),n=new g;return n.x=this.p0.x+e*(this.p1.x-this.p0.x),n.y=this.p0.y+e*(this.p1.y-this.p0.y),n}if(arguments[0]instanceof ce){var i=arguments[0],r=this.projectionFactor(i.p0),s=this.projectionFactor(i.p1);if(r>=1&&s>=1)return null;if(0>=r&&0>=s)return null;var o=this.project(i.p0);0>r&&(o=this.p0),r>1&&(o=this.p1);var a=this.project(i.p1);return 0>s&&(a=this.p0),s>1&&(a=this.p1),new ce(o,a)}},normalize:function(){this.p1.compareTo(this.p0)<0&&this.reverse()},angle:function(){return Math.atan2(this.p1.y-this.p0.y,this.p1.x-this.p0.x)},getCoordinate:function(t){return 0===t?this.p0:this.p1},distancePerpendicular:function(t){return he.distancePointLinePerpendicular(t,this.p0,this.p1)},minY:function(){return Math.min(this.p0.y,this.p1.y)},midPoint:function(){return ce.midPoint(this.p0,this.p1)},projectionFactor:function(t){if(t.equals(this.p0))return 0;if(t.equals(this.p1))return 1;var e=this.p1.x-this.p0.x,n=this.p1.y-this.p0.y,i=e*e+n*n;if(0>=i)return r.NaN;var s=((t.x-this.p0.x)*e+(t.y-this.p0.y)*n)/i;return s},closestPoints:function(t){var e=this.intersection(t);if(null!==e)return[e,e];var n=new Array(2).fill(null),i=r.MAX_VALUE,s=null,o=this.closestPoint(t.p0);i=o.distance(t.p0),n[0]=o,n[1]=t.p0;var a=this.closestPoint(t.p1);s=a.distance(t.p1),i>s&&(i=s,n[0]=a,n[1]=t.p1);var u=t.closestPoint(this.p0);s=u.distance(this.p0),i>s&&(i=s,n[0]=this.p0,n[1]=u);var l=t.closestPoint(this.p1);return s=l.distance(this.p1),i>s&&(i=s,n[0]=this.p1,n[1]=l),n},closestPoint:function(t){var e=this.projectionFactor(t);if(e>0&&1>e)return this.project(t);var n=this.p0.distance(t),i=this.p1.distance(t);return i>n?this.p0:this.p1},maxX:function(){return Math.max(this.p0.x,this.p1.x)},getLength:function(){return this.p0.distance(this.p1)},compareTo:function(t){var e=t,n=this.p0.compareTo(e.p0);return 0!==n?n:this.p1.compareTo(e.p1)},reverse:function(){var t=this.p0;this.p0=this.p1,this.p1=t},equalsTopo:function(t){return this.p0.equals(t.p0)&&this.p1.equals(t.p1)||this.p0.equals(t.p1)&&this.p1.equals(t.p0)},lineIntersection:function(t){try{var e=F.intersection(this.p0,this.p1,t.p0,t.p1);return e}catch(n){if(!(n instanceof w))throw n}finally{}return null},maxY:function(){return Math.max(this.p0.y,this.p1.y)},pointAlongOffset:function(t,e){var n=this.p0.x+t*(this.p1.x-this.p0.x),i=this.p0.y+t*(this.p1.y-this.p0.y),r=this.p1.x-this.p0.x,s=this.p1.y-this.p0.y,o=Math.sqrt(r*r+s*s),a=0,u=0;if(0!==e){if(0>=o)throw new IllegalStateException("Cannot compute offset from zero-length line segment");a=e*r/o,u=e*s/o}var l=n-u,h=i+a,c=new g(l,h);return c},setCoordinates:function(){if(1===arguments.length){var t=arguments[0];this.setCoordinates(t.p0,t.p1)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.p0.x=e.x,this.p0.y=e.y,this.p1.x=n.x,this.p1.y=n.y}},segmentFraction:function(t){var e=this.projectionFactor(t);return 0>e?e=0:(e>1||r.isNaN(e))&&(e=1),e},toString:function(){return"LINESTRING( "+this.p0.x+" "+this.p0.y+", "+this.p1.x+" "+this.p1.y+")"},isHorizontal:function(){return this.p0.y===this.p1.y},distance:function(){if(arguments[0]instanceof ce){var t=arguments[0];return he.distanceLineLine(this.p0,this.p1,t.p0,t.p1)}if(arguments[0]instanceof g){var e=arguments[0];return he.distancePointLine(e,this.p0,this.p1)}},pointAlong:function(t){var e=new g;return e.x=this.p0.x+t*(this.p1.x-this.p0.x),e.y=this.p0.y+t*(this.p1.y-this.p0.y),e},hashCode:function(){var t=java.lang.Double.doubleToLongBits(this.p0.x);t^=31*java.lang.Double.doubleToLongBits(this.p0.y);var e=Math.trunc(t)^Math.trunc(t>>32),n=java.lang.Double.doubleToLongBits(this.p1.x);n^=31*java.lang.Double.doubleToLongBits(this.p1.y);var i=Math.trunc(n)^Math.trunc(n>>32);return e^i},interfaces_:function(){return[s,u]},getClass:function(){return ce}}),ce.midPoint=function(t,e){return new g((t.x+e.x)/2,(t.y+e.y)/2)},ce.serialVersionUID=0x2d2172135f411c00,e(fe.prototype,{isIntersects:function(){return!this.isDisjoint()},isCovers:function(){var t=fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])||fe.isTrue(this.matrix[L.INTERIOR][L.BOUNDARY])||fe.isTrue(this.matrix[L.BOUNDARY][L.INTERIOR])||fe.isTrue(this.matrix[L.BOUNDARY][L.BOUNDARY]);return t&&this.matrix[L.EXTERIOR][L.INTERIOR]===lt.FALSE&&this.matrix[L.EXTERIOR][L.BOUNDARY]===lt.FALSE},isCoveredBy:function(){var t=fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])||fe.isTrue(this.matrix[L.INTERIOR][L.BOUNDARY])||fe.isTrue(this.matrix[L.BOUNDARY][L.INTERIOR])||fe.isTrue(this.matrix[L.BOUNDARY][L.BOUNDARY]);return t&&this.matrix[L.INTERIOR][L.EXTERIOR]===lt.FALSE&&this.matrix[L.BOUNDARY][L.EXTERIOR]===lt.FALSE},set:function(){if(1===arguments.length)for(var t=arguments[0],e=0;e<t.length;e++){var n=Math.trunc(e/3),i=e%3;this.matrix[n][i]=lt.toDimensionValue(t.charAt(e))}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];this.matrix[r][s]=o}},isContains:function(){return fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&this.matrix[L.EXTERIOR][L.INTERIOR]===lt.FALSE&&this.matrix[L.EXTERIOR][L.BOUNDARY]===lt.FALSE},setAtLeast:function(){if(1===arguments.length)for(var t=arguments[0],e=0;e<t.length;e++){var n=Math.trunc(e/3),i=e%3;this.setAtLeast(n,i,lt.toDimensionValue(t.charAt(e)))}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];this.matrix[r][s]<o&&(this.matrix[r][s]=o)}},setAtLeastIfValid:function(t,e,n){t>=0&&e>=0&&this.setAtLeast(t,e,n)},isWithin:function(){return fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&this.matrix[L.INTERIOR][L.EXTERIOR]===lt.FALSE&&this.matrix[L.BOUNDARY][L.EXTERIOR]===lt.FALSE},isTouches:function(t,e){return t>e?this.isTouches(e,t):t===lt.A&&e===lt.A||t===lt.L&&e===lt.L||t===lt.L&&e===lt.A||t===lt.P&&e===lt.A||t===lt.P&&e===lt.L?this.matrix[L.INTERIOR][L.INTERIOR]===lt.FALSE&&(fe.isTrue(this.matrix[L.INTERIOR][L.BOUNDARY])||fe.isTrue(this.matrix[L.BOUNDARY][L.INTERIOR])||fe.isTrue(this.matrix[L.BOUNDARY][L.BOUNDARY])):!1},isOverlaps:function(t,e){return t===lt.P&&e===lt.P||t===lt.A&&e===lt.A?fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&fe.isTrue(this.matrix[L.INTERIOR][L.EXTERIOR])&&fe.isTrue(this.matrix[L.EXTERIOR][L.INTERIOR]):t===lt.L&&e===lt.L?1===this.matrix[L.INTERIOR][L.INTERIOR]&&fe.isTrue(this.matrix[L.INTERIOR][L.EXTERIOR])&&fe.isTrue(this.matrix[L.EXTERIOR][L.INTERIOR]):!1},isEquals:function(t,e){return t!==e?!1:fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&this.matrix[L.INTERIOR][L.EXTERIOR]===lt.FALSE&&this.matrix[L.BOUNDARY][L.EXTERIOR]===lt.FALSE&&this.matrix[L.EXTERIOR][L.INTERIOR]===lt.FALSE&&this.matrix[L.EXTERIOR][L.BOUNDARY]===lt.FALSE},toString:function(){for(var t=new P("123456789"),e=0;3>e;e++)for(var n=0;3>n;n++)t.setCharAt(3*e+n,lt.toDimensionSymbol(this.matrix[e][n]));return t.toString()},setAll:function(t){for(var e=0;3>e;e++)for(var n=0;3>n;n++)this.matrix[e][n]=t},get:function(t,e){return this.matrix[t][e]},transpose:function(){var t=this.matrix[1][0];return this.matrix[1][0]=this.matrix[0][1],this.matrix[0][1]=t,t=this.matrix[2][0],this.matrix[2][0]=this.matrix[0][2],this.matrix[0][2]=t,t=this.matrix[2][1],this.matrix[2][1]=this.matrix[1][2],this.matrix[1][2]=t,this},matches:function(t){if(9!==t.length)throw new i("Should be length 9: "+t);for(var e=0;3>e;e++)for(var n=0;3>n;n++)if(!fe.matches(this.matrix[e][n],t.charAt(3*e+n)))return!1;return!0},add:function(t){for(var e=0;3>e;e++)for(var n=0;3>n;n++)this.setAtLeast(e,n,t.get(e,n))},isDisjoint:function(){return this.matrix[L.INTERIOR][L.INTERIOR]===lt.FALSE&&this.matrix[L.INTERIOR][L.BOUNDARY]===lt.FALSE&&this.matrix[L.BOUNDARY][L.INTERIOR]===lt.FALSE&&this.matrix[L.BOUNDARY][L.BOUNDARY]===lt.FALSE},isCrosses:function(t,e){return t===lt.P&&e===lt.L||t===lt.P&&e===lt.A||t===lt.L&&e===lt.A?fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&fe.isTrue(this.matrix[L.INTERIOR][L.EXTERIOR]):t===lt.L&&e===lt.P||t===lt.A&&e===lt.P||t===lt.A&&e===lt.L?fe.isTrue(this.matrix[L.INTERIOR][L.INTERIOR])&&fe.isTrue(this.matrix[L.EXTERIOR][L.INTERIOR]):t===lt.L&&e===lt.L?0===this.matrix[L.INTERIOR][L.INTERIOR]:!1},interfaces_:function(){return[o]},getClass:function(){return fe}}),fe.matches=function(){if(Number.isInteger(arguments[0])&&"string"==typeof arguments[1]){var t=arguments[0],e=arguments[1];return e===lt.SYM_DONTCARE?!0:e===lt.SYM_TRUE&&(t>=0||t===lt.TRUE)?!0:e===lt.SYM_FALSE&&t===lt.FALSE?!0:e===lt.SYM_P&&t===lt.P?!0:e===lt.SYM_L&&t===lt.L?!0:e===lt.SYM_A&&t===lt.A}if("string"==typeof arguments[0]&&"string"==typeof arguments[1]){var n=arguments[0],i=arguments[1],r=new fe(n);return r.matches(i)}},fe.isTrue=function(t){return t>=0||t===lt.TRUE};var lo=Object.freeze({Coordinate:g,CoordinateList:N,Envelope:C,LineSegment:ce,GeometryFactory:ie,Geometry:B,Point:Lt,LineString:St,LinearRing:bt,Polygon:Tt,GeometryCollection:ft,MultiPoint:Pt,MultiLineString:gt,MultiPolygon:Ot,Dimension:lt,IntersectionMatrix:fe});e(ge.prototype,{addPoint:function(t){this.ptCount+=1,this.ptCentSum.x+=t.x,this.ptCentSum.y+=t.y},setBasePoint:function(t){null===this.areaBasePt&&(this.areaBasePt=t)},addLineSegments:function(t){for(var e=0,n=0;n<t.length-1;n++){var i=t[n].distance(t[n+1]);if(0!==i){e+=i;var r=(t[n].x+t[n+1].x)/2;this.lineCentSum.x+=i*r;var s=(t[n].y+t[n+1].y)/2;this.lineCentSum.y+=i*s}}this.totalLength+=e,0===e&&t.length>0&&this.addPoint(t[0])},addHole:function(t){for(var e=he.isCCW(t),n=0;n<t.length-1;n++)this.addTriangle(this.areaBasePt,t[n],t[n+1],e);this.addLineSegments(t)},getCentroid:function(){var t=new g;if(Math.abs(this.areasum2)>0)t.x=this.cg3.x/3/this.areasum2,t.y=this.cg3.y/3/this.areasum2;else if(this.totalLength>0)t.x=this.lineCentSum.x/this.totalLength,t.y=this.lineCentSum.y/this.totalLength;else{if(!(this.ptCount>0))return null;t.x=this.ptCentSum.x/this.ptCount,t.y=this.ptCentSum.y/this.ptCount}return t},addShell:function(t){t.length>0&&this.setBasePoint(t[0]);for(var e=!he.isCCW(t),n=0;n<t.length-1;n++)this.addTriangle(this.areaBasePt,t[n],t[n+1],e);this.addLineSegments(t)},addTriangle:function(t,e,n,i){var r=i?1:-1;ge.centroid3(t,e,n,this.triangleCent3);var s=ge.area2(t,e,n);this.cg3.x+=r*s*this.triangleCent3.x,this.cg3.y+=r*s*this.triangleCent3.y,this.areasum2+=r*s},add:function(){if(arguments[0]instanceof Tt){var t=arguments[0];this.addShell(t.getExteriorRing().getCoordinates());for(var e=0;e<t.getNumInteriorRing();e++)this.addHole(t.getInteriorRingN(e).getCoordinates())}else if(arguments[0]instanceof B){var n=arguments[0];if(n.isEmpty())return null;if(n instanceof Lt)this.addPoint(n.getCoordinate());else if(n instanceof St)this.addLineSegments(n.getCoordinates());else if(n instanceof Tt){var i=n;this.add(i)}else if(n instanceof ft)for(var r=n,e=0;e<r.getNumGeometries();e++)this.add(r.getGeometryN(e))}},interfaces_:function(){return[]},getClass:function(){return ge}}),ge.area2=function(t,e,n){return(e.x-t.x)*(n.y-t.y)-(n.x-t.x)*(e.y-t.y)},ge.centroid3=function(t,e,n,i){return i.x=t.x+e.x+n.x,i.y=t.y+e.y+n.y,null},ge.getCentroid=function(t){var e=new ge(t);return e.getCentroid()},de.prototype=new Error,de.prototype.name="EmptyStackException",pe.prototype=new y,pe.prototype.add=function(t){return this.array_.push(t),!0},pe.prototype.get=function(t){if(0>t||t>=this.size())throw new IndexOutOfBoundsException;return this.array_[t]},pe.prototype.push=function(t){return this.array_.push(t),t},pe.prototype.pop=function(t){if(0===this.array_.length)throw new de;return this.array_.pop()},pe.prototype.peek=function(){if(0===this.array_.length)throw new de;return this.array_[this.array_.length-1]},pe.prototype.empty=function(){return 0===this.array_.length},pe.prototype.isEmpty=function(){return this.empty()},pe.prototype.search=function(t){return this.array_.indexOf(t)},pe.prototype.size=function(){return this.array_.length},pe.prototype.toArray=function(){for(var t=[],e=0,n=this.array_.length;n>e;e++)t.push(this.array_[e]);return t},e(ve.prototype,{filter:function(t){this.treeSet.contains(t)||(this.list.add(t),this.treeSet.add(t))},getCoordinates:function(){var t=new Array(this.list.size()).fill(null);return this.list.toArray(t)},interfaces_:function(){return[z]},getClass:function(){return ve}}),ve.filterCoordinates=function(t){for(var e=new ve,n=0;n<t.length;n++)e.filter(t[n]);return e.getCoordinates()},e(me.prototype,{preSort:function(t){for(var e=null,n=1;n<t.length;n++)(t[n].y<t[0].y||t[n].y===t[0].y&&t[n].x<t[0].x)&&(e=t[0],t[0]=t[n],t[n]=e);return ut.sort(t,1,t.length,new ye(t[0])),t},computeOctRing:function(t){var e=this.computeOctPts(t),n=new N;return n.add(e,!1),n.size()<3?null:(n.closeRing(),n.toCoordinateArray())},lineOrPolygon:function(t){if(t=this.cleanRing(t),3===t.length)return this.geomFactory.createLineString([t[0],t[1]]);var e=this.geomFactory.createLinearRing(t);return this.geomFactory.createPolygon(e,null)},cleanRing:function(t){f.equals(t[0],t[t.length-1]);for(var e=new I,n=null,i=0;i<=t.length-2;i++){var r=t[i],s=t[i+1];r.equals(s)||null!==n&&this.isBetween(n,r,s)||(e.add(r),n=r)}e.add(t[t.length-1]);var o=new Array(e.size()).fill(null);return e.toArray(o)},isBetween:function(t,e,n){if(0!==he.computeOrientation(t,e,n))return!1;if(t.x!==n.x){if(t.x<=e.x&&e.x<=n.x)return!0;if(n.x<=e.x&&e.x<=t.x)return!0}if(t.y!==n.y){if(t.y<=e.y&&e.y<=n.y)return!0;if(n.y<=e.y&&e.y<=t.y)return!0}return!1},reduce:function(t){var e=this.computeOctRing(t);if(null===e)return t;for(var n=new at,i=0;i<e.length;i++)n.add(e[i]);for(var i=0;i<t.length;i++)he.isPointInRing(t[i],e)||n.add(t[i]);var r=H.toCoordinateArray(n);return r.length<3?this.padArray3(r):r},getConvexHull:function(){if(0===this.inputPts.length)return this.geomFactory.createGeometryCollection(null);if(1===this.inputPts.length)return this.geomFactory.createPoint(this.inputPts[0]);if(2===this.inputPts.length)return this.geomFactory.createLineString(this.inputPts);var t=this.inputPts;this.inputPts.length>50&&(t=this.reduce(this.inputPts));var e=this.preSort(t),n=this.grahamScan(e),i=this.toCoordinateArray(n);return this.lineOrPolygon(i)},padArray3:function(t){for(var e=new Array(3).fill(null),n=0;n<e.length;n++)n<t.length?e[n]=t[n]:e[n]=t[0];return e},computeOctPts:function(t){for(var e=new Array(8).fill(null),n=0;n<e.length;n++)e[n]=t[0];for(var i=1;i<t.length;i++)t[i].x<e[0].x&&(e[0]=t[i]),t[i].x-t[i].y<e[1].x-e[1].y&&(e[1]=t[i]),t[i].y>e[2].y&&(e[2]=t[i]),t[i].x+t[i].y>e[3].x+e[3].y&&(e[3]=t[i]),t[i].x>e[4].x&&(e[4]=t[i]),t[i].x-t[i].y>e[5].x-e[5].y&&(e[5]=t[i]),t[i].y<e[6].y&&(e[6]=t[i]),t[i].x+t[i].y<e[7].x+e[7].y&&(e[7]=t[i]);return e},toCoordinateArray:function(t){for(var e=new Array(t.size()).fill(null),n=0;n<t.size();n++){var i=t.get(n);e[n]=i}return e},grahamScan:function(t){var e=null,n=new pe;e=n.push(t[0]),e=n.push(t[1]),e=n.push(t[2]);for(var i=3;i<t.length;i++){for(e=n.pop();!n.empty()&&he.computeOrientation(n.peek(),e,t[i])>0;)e=n.pop();e=n.push(e),e=n.push(t[i])}return e=n.push(t[0]),n},interfaces_:function(){return[]},getClass:function(){return me}}),me.extractCoordinates=function(t){var e=new ve;return t.apply(e),e.getCoordinates();
},e(ye.prototype,{compare:function(t,e){var n=t,i=e;return ye.polarCompare(this.origin,n,i)},interfaces_:function(){return[a]},getClass:function(){return ye}}),ye.polarCompare=function(t,e,n){var i=e.x-t.x,r=e.y-t.y,s=n.x-t.x,o=n.y-t.y,a=he.computeOrientation(t,e,n);if(a===he.COUNTERCLOCKWISE)return 1;if(a===he.CLOCKWISE)return-1;var u=i*i+r*r,l=s*s+o*o;return l>u?-1:u>l?1:0},me.RadialComparator=ye,e(xe.prototype,{transformPoint:function(t,e){return this.factory.createPoint(this.transformCoordinates(t.getCoordinateSequence(),t))},transformPolygon:function(t,e){var n=!0,i=this.transformLinearRing(t.getExteriorRing(),t);null!==i&&i instanceof bt&&!i.isEmpty()||(n=!1);for(var r=new I,s=0;s<t.getNumInteriorRing();s++){var o=this.transformLinearRing(t.getInteriorRingN(s),t);null===o||o.isEmpty()||(o instanceof bt||(n=!1),r.add(o))}if(n)return this.factory.createPolygon(i,r.toArray([]));var a=new I;return null!==i&&a.add(i),a.addAll(r),this.factory.buildGeometry(a)},createCoordinateSequence:function(t){return this.factory.getCoordinateSequenceFactory().create(t)},getInputGeometry:function(){return this.inputGeom},transformMultiLineString:function(t,e){for(var n=new I,i=0;i<t.getNumGeometries();i++){var r=this.transformLineString(t.getGeometryN(i),t);null!==r&&(r.isEmpty()||n.add(r))}return this.factory.buildGeometry(n)},transformCoordinates:function(t,e){return this.copy(t)},transformLineString:function(t,e){return this.factory.createLineString(this.transformCoordinates(t.getCoordinateSequence(),t))},transformMultiPoint:function(t,e){for(var n=new I,i=0;i<t.getNumGeometries();i++){var r=this.transformPoint(t.getGeometryN(i),t);null!==r&&(r.isEmpty()||n.add(r))}return this.factory.buildGeometry(n)},transformMultiPolygon:function(t,e){for(var n=new I,i=0;i<t.getNumGeometries();i++){var r=this.transformPolygon(t.getGeometryN(i),t);null!==r&&(r.isEmpty()||n.add(r))}return this.factory.buildGeometry(n)},copy:function(t){return t.copy()},transformGeometryCollection:function(t,e){for(var n=new I,i=0;i<t.getNumGeometries();i++){var r=this.transform(t.getGeometryN(i));null!==r&&(this.pruneEmptyGeometry&&r.isEmpty()||n.add(r))}return this.preserveGeometryCollectionType?this.factory.createGeometryCollection(ie.toGeometryArray(n)):this.factory.buildGeometry(n)},transform:function(t){if(this.inputGeom=t,this.factory=t.getFactory(),t instanceof Lt)return this.transformPoint(t,null);if(t instanceof Pt)return this.transformMultiPoint(t,null);if(t instanceof bt)return this.transformLinearRing(t,null);if(t instanceof St)return this.transformLineString(t,null);if(t instanceof gt)return this.transformMultiLineString(t,null);if(t instanceof Tt)return this.transformPolygon(t,null);if(t instanceof Ot)return this.transformMultiPolygon(t,null);if(t instanceof ft)return this.transformGeometryCollection(t,null);throw new i("Unknown Geometry subtype: "+t.getClass().getName())},transformLinearRing:function(t,e){var n=this.transformCoordinates(t.getCoordinateSequence(),t);if(null===n)return this.factory.createLinearRing(null);var i=n.size();return i>0&&4>i&&!this.preserveType?this.factory.createLineString(n):this.factory.createLinearRing(n)},interfaces_:function(){return[]},getClass:function(){return xe}}),e(Ee.prototype,{snapVertices:function(t,e){for(var n=this._isClosed?t.size()-1:t.size(),i=0;n>i;i++){var r=t.get(i),s=this.findSnapForVertex(r,e);null!==s&&(t.set(i,new g(s)),0===i&&this._isClosed&&t.set(t.size()-1,new g(s)))}},findSnapForVertex:function(t,e){for(var n=0;n<e.length;n++){if(t.equals2D(e[n]))return null;if(t.distance(e[n])<this.snapTolerance)return e[n]}return null},snapTo:function(t){var e=new N(this.srcPts);this.snapVertices(e,t),this.snapSegments(e,t);var n=e.toCoordinateArray();return n},snapSegments:function(t,e){if(0===e.length)return null;var n=e.length;e[0].equals2D(e[e.length-1])&&(n=e.length-1);for(var i=0;n>i;i++){var r=e[i],s=this.findSegmentIndexToSnap(r,t);s>=0&&t.add(s+1,new g(r),!1)}},findSegmentIndexToSnap:function(t,e){for(var n=r.MAX_VALUE,i=-1,s=0;s<e.size()-1;s++){if(this.seg.p0=e.get(s),this.seg.p1=e.get(s+1),this.seg.p0.equals2D(t)||this.seg.p1.equals2D(t)){if(this.allowSnappingToSourceVertices)continue;return-1}var o=this.seg.distance(t);o<this.snapTolerance&&n>o&&(n=o,i=s)}return i},setAllowSnappingToSourceVertices:function(t){this.allowSnappingToSourceVertices=t},interfaces_:function(){return[]},getClass:function(){return Ee}}),Ee.isClosed=function(t){return t.length<=1?!1:t[0].equals2D(t[t.length-1])},e(Ie.prototype,{snapTo:function(t,e){var n=this.extractTargetCoordinates(t),i=new Ne(e,n);return i.transform(this.srcGeom)},snapToSelf:function(t,e){var n=this.extractTargetCoordinates(this.srcGeom),i=new Ne(t,n,!0),r=i.transform(this.srcGeom),s=r;return e&&R(s,Rt)&&(s=r.buffer(0)),s},computeSnapTolerance:function(t){var e=this.computeMinimumSegmentLength(t),n=e/10;return n},extractTargetCoordinates:function(t){for(var e=new at,n=t.getCoordinates(),i=0;i<n.length;i++)e.add(n[i]);return e.toArray(new Array(0).fill(null))},computeMinimumSegmentLength:function(t){for(var e=r.MAX_VALUE,n=0;n<t.length-1;n++){var i=t[n].distance(t[n+1]);e>i&&(e=i)}return e},interfaces_:function(){return[]},getClass:function(){return Ie}}),Ie.snap=function(t,e,n){var i=new Array(2).fill(null),r=new Ie(t);i[0]=r.snapTo(e,n);var s=new Ie(e);return i[1]=s.snapTo(i[0],n),i},Ie.computeOverlaySnapTolerance=function(){if(1===arguments.length){var t=arguments[0],e=Ie.computeSizeBasedSnapTolerance(t),n=t.getPrecisionModel();if(n.getType()===ee.FIXED){var i=1/n.getScale()*2/1.415;i>e&&(e=i)}return e}if(2===arguments.length){var r=arguments[0],s=arguments[1];return Math.min(Ie.computeOverlaySnapTolerance(r),Ie.computeOverlaySnapTolerance(s))}},Ie.computeSizeBasedSnapTolerance=function(t){var e=t.getEnvelopeInternal(),n=Math.min(e.getHeight(),e.getWidth()),i=n*Ie.SNAP_PRECISION_FACTOR;return i},Ie.snapToSelf=function(t,e,n){var i=new Ie(t);return i.snapToSelf(e,n)},Ie.SNAP_PRECISION_FACTOR=1e-9,h(Ne,xe),e(Ne.prototype,{snapLine:function(t,e){var n=new Ee(t,this.snapTolerance);return n.setAllowSnappingToSourceVertices(this.isSelfSnap),n.snapTo(e)},transformCoordinates:function(t,e){var n=t.toCoordinateArray(),i=this.snapLine(n,this.snapPts);return this.factory.getCoordinateSequenceFactory().create(i)},interfaces_:function(){return[]},getClass:function(){return Ne}}),e(Ce.prototype,{getCommon:function(){return r.longBitsToDouble(this.commonBits)},add:function(t){var e=r.doubleToLongBits(t);if(this.isFirst)return this.commonBits=e,this.commonSignExp=Ce.signExpBits(this.commonBits),this.isFirst=!1,null;var n=Ce.signExpBits(e);return n!==this.commonSignExp?(this.commonBits=0,null):(this.commonMantissaBitsCount=Ce.numCommonMostSigMantissaBits(this.commonBits,e),void(this.commonBits=Ce.zeroLowerBits(this.commonBits,64-(12+this.commonMantissaBitsCount))))},toString:function(){if(1===arguments.length){var t=arguments[0],e=r.longBitsToDouble(t),n=Long.toBinaryString(t),i="0000000000000000000000000000000000000000000000000000000000000000"+n,s=i.substring(i.length-64),o=s.substring(0,1)+"  "+s.substring(1,12)+"(exp) "+s.substring(12)+" [ "+e+" ]";return o}},interfaces_:function(){return[]},getClass:function(){return Ce}}),Ce.getBit=function(t,e){var n=1<<e;return 0!==(t&n)?1:0},Ce.signExpBits=function(t){return t>>52},Ce.zeroLowerBits=function(t,e){var n=(1<<e)-1,i=~n,r=t&i;return r},Ce.numCommonMostSigMantissaBits=function(t,e){for(var n=0,i=52;i>=0;i--){if(Ce.getBit(t,i)!==Ce.getBit(e,i))return n;n++}return 52},e(Se.prototype,{addCommonBits:function(t){var e=new Le(this.commonCoord);t.apply(e),t.geometryChanged()},removeCommonBits:function(t){if(0===this.commonCoord.x&&0===this.commonCoord.y)return t;var e=new g(this.commonCoord);e.x=-e.x,e.y=-e.y;var n=new Le(e);return t.apply(n),t.geometryChanged(),t},getCommonCoordinate:function(){return this.commonCoord},add:function(t){t.apply(this.ccFilter),this.commonCoord=this.ccFilter.getCommonCoordinate()},interfaces_:function(){return[]},getClass:function(){return Se}}),e(we.prototype,{filter:function(t){this.commonBitsX.add(t.x),this.commonBitsY.add(t.y)},getCommonCoordinate:function(){return new g(this.commonBitsX.getCommon(),this.commonBitsY.getCommon())},interfaces_:function(){return[z]},getClass:function(){return we}}),e(Le.prototype,{filter:function(t,e){var n=t.getOrdinate(e,0)+this.trans.x,i=t.getOrdinate(e,1)+this.trans.y;t.setOrdinate(e,0,n),t.setOrdinate(e,1,i)},isDone:function(){return!1},isGeometryChanged:function(){return!0},interfaces_:function(){return[ct]},getClass:function(){return Le}}),Se.CommonCoordinateFilter=we,Se.Translater=Le,e(Re.prototype,{next:function(){if(this.atStart)return this.atStart=!1,Re.isAtomic(this.parent)&&this.index++,this.parent;if(null!==this.subcollectionIterator){if(this.subcollectionIterator.hasNext())return this.subcollectionIterator.next();this.subcollectionIterator=null}if(this.index>=this.max)throw new x;var t=this.parent.getGeometryN(this.index++);return t instanceof ft?(this.subcollectionIterator=new Re(t),this.subcollectionIterator.next()):t},remove:function(){throw new UnsupportedOperationException(this.getClass().getName())},hasNext:function(){if(this.atStart)return!0;if(null!==this.subcollectionIterator){if(this.subcollectionIterator.hasNext())return!0;this.subcollectionIterator=null}return!(this.index>=this.max)},interfaces_:function(){return[p]},getClass:function(){return Re}}),Re.isAtomic=function(t){return!(t instanceof ft)},e(Te.prototype,{locateInternal:function(){if(arguments[0]instanceof g&&arguments[1]instanceof Tt){var t=arguments[0],e=arguments[1];if(e.isEmpty())return L.EXTERIOR;var n=e.getExteriorRing(),i=this.locateInPolygonRing(t,n);if(i===L.EXTERIOR)return L.EXTERIOR;if(i===L.BOUNDARY)return L.BOUNDARY;for(var r=0;r<e.getNumInteriorRing();r++){var s=e.getInteriorRingN(r),o=this.locateInPolygonRing(t,s);if(o===L.INTERIOR)return L.EXTERIOR;if(o===L.BOUNDARY)return L.BOUNDARY}return L.INTERIOR}if(arguments[0]instanceof g&&arguments[1]instanceof St){var a=arguments[0],u=arguments[1];if(!u.getEnvelopeInternal().intersects(a))return L.EXTERIOR;var l=u.getCoordinates();return u.isClosed()||!a.equals(l[0])&&!a.equals(l[l.length-1])?he.isOnLine(a,l)?L.INTERIOR:L.EXTERIOR:L.BOUNDARY}if(arguments[0]instanceof g&&arguments[1]instanceof Lt){var h=arguments[0],c=arguments[1],f=c.getCoordinate();return f.equals2D(h)?L.INTERIOR:L.EXTERIOR}},locateInPolygonRing:function(t,e){return e.getEnvelopeInternal().intersects(t)?he.locatePointInRing(t,e.getCoordinates()):L.EXTERIOR},intersects:function(t,e){return this.locate(t,e)!==L.EXTERIOR},updateLocationInfo:function(t){t===L.INTERIOR&&(this.isIn=!0),t===L.BOUNDARY&&this.numBoundaries++},computeLocation:function(t,e){if(e instanceof Lt&&this.updateLocationInfo(this.locateInternal(t,e)),e instanceof St)this.updateLocationInfo(this.locateInternal(t,e));else if(e instanceof Tt)this.updateLocationInfo(this.locateInternal(t,e));else if(e instanceof gt)for(var n=e,i=0;i<n.getNumGeometries();i++){var r=n.getGeometryN(i);this.updateLocationInfo(this.locateInternal(t,r))}else if(e instanceof Ot)for(var s=e,i=0;i<s.getNumGeometries();i++){var o=s.getGeometryN(i);this.updateLocationInfo(this.locateInternal(t,o))}else if(e instanceof ft)for(var a=new Re(e);a.hasNext();){var u=a.next();u!==e&&this.computeLocation(t,u)}},locate:function(t,e){return e.isEmpty()?L.EXTERIOR:e instanceof St?this.locateInternal(t,e):e instanceof Tt?this.locateInternal(t,e):(this.isIn=!1,this.numBoundaries=0,this.computeLocation(t,e),this.boundaryRule.isInBoundary(this.numBoundaries)?L.BOUNDARY:this.numBoundaries>0||this.isIn?L.INTERIOR:L.EXTERIOR)},interfaces_:function(){return[]},getClass:function(){return Te}}),e(Pe.prototype,{interfaces_:function(){return[]},getClass:function(){return Pe}}),Pe.octant=function(){if("number"==typeof arguments[0]&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1];if(0===t&&0===e)throw new i("Cannot compute the octant for point ( "+t+", "+e+" )");var n=Math.abs(t),r=Math.abs(e);return t>=0?e>=0?n>=r?0:1:n>=r?7:6:e>=0?n>=r?3:2:n>=r?4:5}if(arguments[0]instanceof g&&arguments[1]instanceof g){var s=arguments[0],o=arguments[1],a=o.x-s.x,u=o.y-s.y;if(0===a&&0===u)throw new i("Cannot compute the octant for two identical points "+s);return Pe.octant(a,u)}},e(be.prototype,{getCoordinates:function(){},size:function(){},getCoordinate:function(t){},isClosed:function(){},setData:function(t){},getData:function(){},interfaces_:function(){return[]},getClass:function(){return be}}),e(Oe.prototype,{getCoordinates:function(){return this.pts},size:function(){return this.pts.length},getCoordinate:function(t){return this.pts[t]},isClosed:function(){return this.pts[0].equals(this.pts[this.pts.length-1])},getSegmentOctant:function(t){return t===this.pts.length-1?-1:Pe.octant(this.getCoordinate(t),this.getCoordinate(t+1))},setData:function(t){this.data=t},getData:function(){return this.data},toString:function(){return se.toLineString(new Gt(this.pts))},interfaces_:function(){return[be]},getClass:function(){return Oe}}),e(_e.prototype,{getBounds:function(){},interfaces_:function(){return[]},getClass:function(){return _e}}),e(Me.prototype,{getItem:function(){return this.item},getBounds:function(){return this.bounds},interfaces_:function(){return[_e,u]},getClass:function(){return Me}}),e(De.prototype,{poll:function(){if(this.isEmpty())return null;var t=this.items.get(1);return this.items.set(1,this.items.get(this._size)),this._size-=1,this.reorder(1),t},size:function(){return this._size},reorder:function(t){for(var e=null,n=this.items.get(t);2*t<=this._size&&(e=2*t,e!==this._size&&this.items.get(e+1).compareTo(this.items.get(e))<0&&e++,this.items.get(e).compareTo(n)<0);t=e)this.items.set(t,this.items.get(e));this.items.set(t,n)},clear:function(){this._size=0,this.items.clear()},isEmpty:function(){return 0===this._size},add:function(t){this.items.add(null),this._size+=1;var e=this._size;for(this.items.set(0,t);t.compareTo(this.items.get(Math.trunc(e/2)))<0;e/=2)this.items.set(e,this.items.get(Math.trunc(e/2)));this.items.set(e,t)},interfaces_:function(){return[]},getClass:function(){return De}}),e(Ae.prototype,{visitItem:function(t){},interfaces_:function(){return[]},getClass:function(){return Ae}}),e(Fe.prototype,{insert:function(t,e){},remove:function(t,e){},query:function(){if(1===arguments.length){arguments[0]}else if(2===arguments.length){arguments[0],arguments[1]}},interfaces_:function(){return[]},getClass:function(){return Fe}}),e(Ge.prototype,{getLevel:function(){return this.level},size:function(){return this.childBoundables.size()},getChildBoundables:function(){return this.childBoundables},addChildBoundable:function(t){f.isTrue(null===this.bounds),this.childBoundables.add(t)},isEmpty:function(){return this.childBoundables.isEmpty()},getBounds:function(){return null===this.bounds&&(this.bounds=this.computeBounds()),this.bounds},interfaces_:function(){return[_e,u]},getClass:function(){return Ge}}),Ge.serialVersionUID=0x5a1e55ec41369800;var ho={reverseOrder:function(){return{compare:function(t,e){return e.compareTo(t)}}},min:function(t){return ho.sort(t),t.get(0)},sort:function(t,e){var n=t.toArray();e?ut.sort(n,e):ut.sort(n);for(var i=t.iterator(),r=0,s=n.length;s>r;r++)i.next(),i.set(n[r])},singletonList:function(t){var e=new I;return e.add(t),e}};e(qe.prototype,{expandToQueue:function(t,e){var n=qe.isComposite(this.boundable1),r=qe.isComposite(this.boundable2);if(n&&r)return qe.area(this.boundable1)>qe.area(this.boundable2)?(this.expand(this.boundable1,this.boundable2,t,e),null):(this.expand(this.boundable2,this.boundable1,t,e),null);if(n)return this.expand(this.boundable1,this.boundable2,t,e),null;if(r)return this.expand(this.boundable2,this.boundable1,t,e),null;throw new i("neither boundable is composite")},isLeaves:function(){return!(qe.isComposite(this.boundable1)||qe.isComposite(this.boundable2))},compareTo:function(t){var e=t;return this._distance<e._distance?-1:this._distance>e._distance?1:0},expand:function(t,e,n,i){for(var r=t.getChildBoundables(),s=r.iterator();s.hasNext();){var o=s.next(),a=new qe(o,e,this.itemDistance);a.getDistance()<i&&n.add(a)}},getBoundable:function(t){return 0===t?this.boundable1:this.boundable2},getDistance:function(){return this._distance},distance:function(){return this.isLeaves()?this.itemDistance.distance(this.boundable1,this.boundable2):this.boundable1.getBounds().distance(this.boundable2.getBounds())},interfaces_:function(){return[s]},getClass:function(){return qe}}),qe.area=function(t){return t.getBounds().getArea()},qe.isComposite=function(t){return t instanceof Ge},e(Be.prototype,{getNodeCapacity:function(){return this.nodeCapacity},lastNode:function(t){return t.get(t.size()-1)},size:function qo(){if(0===arguments.length)return this.isEmpty()?0:(this.build(),this.size(this.root));if(1===arguments.length){for(var t=arguments[0],qo=0,e=t.getChildBoundables().iterator();e.hasNext();){var n=e.next();n instanceof Ge?qo+=this.size(n):n instanceof Me&&(qo+=1)}return qo}},removeItem:function(t,e){for(var n=null,i=t.getChildBoundables().iterator();i.hasNext();){var r=i.next();r instanceof Me&&r.getItem()===e&&(n=r)}return null!==n?(t.getChildBoundables().remove(n),!0):!1},itemsTree:function(){if(0===arguments.length){this.build();var t=this.itemsTree(this.root);return null===t?new I:t}if(1===arguments.length){for(var e=arguments[0],n=new I,i=e.getChildBoundables().iterator();i.hasNext();){var r=i.next();if(r instanceof Ge){var s=this.itemsTree(r);null!==s&&n.add(s)}else r instanceof Me?n.add(r.getItem()):f.shouldNeverReachHere()}return n.size()<=0?null:n}},insert:function(t,e){f.isTrue(!this.built,"Cannot insert items into an STR packed R-tree after it has been built."),this.itemBoundables.add(new Me(t,e))},boundablesAtLevel:function(){if(1===arguments.length){var t=arguments[0],e=new I;return this.boundablesAtLevel(t,this.root,e),e}if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];if(f.isTrue(n>-2),i.getLevel()===n)return r.add(i),null;for(var s=i.getChildBoundables().iterator();s.hasNext();){var o=s.next();o instanceof Ge?this.boundablesAtLevel(n,o,r):(f.isTrue(o instanceof Me),-1===n&&r.add(o))}return null}},query:function(){if(1===arguments.length){var t=arguments[0];this.build();var e=new I;return this.isEmpty()?e:(this.getIntersectsOp().intersects(this.root.getBounds(),t)&&this.query(t,this.root,e),e)}if(2===arguments.length){var n=arguments[0],i=arguments[1];if(this.build(),this.isEmpty())return null;this.getIntersectsOp().intersects(this.root.getBounds(),n)&&this.query(n,this.root,i)}else if(3===arguments.length)if(R(arguments[2],Ae)&&arguments[0]instanceof Object&&arguments[1]instanceof Ge)for(var r=arguments[0],s=arguments[1],o=arguments[2],a=s.getChildBoundables(),u=0;u<a.size();u++){var l=a.get(u);this.getIntersectsOp().intersects(l.getBounds(),r)&&(l instanceof Ge?this.query(r,l,o):l instanceof Me?o.visitItem(l.getItem()):f.shouldNeverReachHere())}else if(R(arguments[2],y)&&arguments[0]instanceof Object&&arguments[1]instanceof Ge)for(var h=arguments[0],c=arguments[1],g=arguments[2],a=c.getChildBoundables(),u=0;u<a.size();u++){var l=a.get(u);this.getIntersectsOp().intersects(l.getBounds(),h)&&(l instanceof Ge?this.query(h,l,g):l instanceof Me?g.add(l.getItem()):f.shouldNeverReachHere())}},build:function(){return this.built?null:(this.root=this.itemBoundables.isEmpty()?this.createNode(0):this.createHigherLevels(this.itemBoundables,-1),this.itemBoundables=null,void(this.built=!0))},getRoot:function(){return this.build(),this.root},remove:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.build(),this.getIntersectsOp().intersects(this.root.getBounds(),t)?this.remove(t,this.root,e):!1}if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2],s=this.removeItem(i,r);if(s)return!0;for(var o=null,a=i.getChildBoundables().iterator();a.hasNext();){var u=a.next();if(this.getIntersectsOp().intersects(u.getBounds(),n)&&u instanceof Ge&&(s=this.remove(n,u,r))){o=u;break}}return null!==o&&o.getChildBoundables().isEmpty()&&i.getChildBoundables().remove(o),s}},createHigherLevels:function(t,e){f.isTrue(!t.isEmpty());var n=this.createParentBoundables(t,e+1);return 1===n.size()?n.get(0):this.createHigherLevels(n,e+1)},depth:function(){if(0===arguments.length)return this.isEmpty()?0:(this.build(),this.depth(this.root));if(1===arguments.length){for(var t=arguments[0],e=0,n=t.getChildBoundables().iterator();n.hasNext();){var i=n.next();if(i instanceof Ge){var r=this.depth(i);r>e&&(e=r)}}return e+1}},createParentBoundables:function(t,e){f.isTrue(!t.isEmpty());var n=new I;n.add(this.createNode(e));var i=new I(t);ho.sort(i,this.getComparator());for(var r=i.iterator();r.hasNext();){var s=r.next();this.lastNode(n).getChildBoundables().size()===this.getNodeCapacity()&&n.add(this.createNode(e)),this.lastNode(n).addChildBoundable(s)}return n},isEmpty:function(){return this.built?this.root.isEmpty():this.itemBoundables.isEmpty()},interfaces_:function(){return[u]},getClass:function(){return Be}}),Be.compareDoubles=function(t,e){return t>e?1:e>t?-1:0},Be.IntersectsOp=ze,Be.serialVersionUID=-0x35ef64c82d4c5400,Be.DEFAULT_NODE_CAPACITY=10,e(Ve.prototype,{distance:function(t,e){},interfaces_:function(){return[]},getClass:function(){return Ve}}),h(ke,Be),e(ke.prototype,{createParentBoundablesFromVerticalSlices:function(t,e){f.isTrue(t.length>0);for(var n=new I,i=0;i<t.length;i++)n.addAll(this.createParentBoundablesFromVerticalSlice(t[i],e));return n},createNode:function(t){return new Ye(t)},size:function(){return 0===arguments.length?Be.prototype.size.call(this):Be.prototype.size.apply(this,arguments)},insert:function(){if(2!==arguments.length)return Be.prototype.insert.apply(this,arguments);var t=arguments[0],e=arguments[1];return t.isNull()?null:void Be.prototype.insert.call(this,t,e)},getIntersectsOp:function(){return ke.intersectsOp},verticalSlices:function(t,e){for(var n=Math.trunc(Math.ceil(t.size()/e)),i=new Array(e).fill(null),r=t.iterator(),s=0;e>s;s++){i[s]=new I;for(var o=0;r.hasNext()&&n>o;){var a=r.next();i[s].add(a),o++}}return i},query:function(){if(1===arguments.length){var t=arguments[0];return Be.prototype.query.call(this,t)}if(2===arguments.length){var e=arguments[0],n=arguments[1];Be.prototype.query.call(this,e,n)}else if(3===arguments.length)if(R(arguments[2],Ae)&&arguments[0]instanceof Object&&arguments[1]instanceof Ge){var i=arguments[0],r=arguments[1],s=arguments[2];Be.prototype.query.call(this,i,r,s)}else if(R(arguments[2],y)&&arguments[0]instanceof Object&&arguments[1]instanceof Ge){var o=arguments[0],a=arguments[1],u=arguments[2];Be.prototype.query.call(this,o,a,u)}},getComparator:function(){return ke.yComparator},createParentBoundablesFromVerticalSlice:function(t,e){return Be.prototype.createParentBoundables.call(this,t,e)},remove:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return Be.prototype.remove.call(this,t,e)}return Be.prototype.remove.apply(this,arguments)},depth:function(){return 0===arguments.length?Be.prototype.depth.call(this):Be.prototype.depth.apply(this,arguments)},createParentBoundables:function(t,e){f.isTrue(!t.isEmpty());var n=Math.trunc(Math.ceil(t.size()/this.getNodeCapacity())),i=new I(t);ho.sort(i,ke.xComparator);var r=this.verticalSlices(i,Math.trunc(Math.ceil(Math.sqrt(n))));return this.createParentBoundablesFromVerticalSlices(r,e)},nearestNeighbour:function(){if(1===arguments.length){if(R(arguments[0],Ve)){var t=arguments[0],e=new qe(this.getRoot(),this.getRoot(),t);return this.nearestNeighbour(e)}if(arguments[0]instanceof qe){var n=arguments[0];return this.nearestNeighbour(n,r.POSITIVE_INFINITY)}}else if(2===arguments.length){if(arguments[0]instanceof ke&&R(arguments[1],Ve)){var i=arguments[0],s=arguments[1],e=new qe(this.getRoot(),i.getRoot(),s);return this.nearestNeighbour(e)}if(arguments[0]instanceof qe&&"number"==typeof arguments[1]){var o=arguments[0],a=arguments[1],u=a,l=null,h=new De;for(h.add(o);!h.isEmpty()&&u>0;){var c=h.poll(),f=c.getDistance();if(f>=u)break;c.isLeaves()?(u=f,l=c):c.expandToQueue(h,u)}return[l.getBoundable(0).getItem(),l.getBoundable(1).getItem()]}}else if(3===arguments.length){var g=arguments[0],d=arguments[1],p=arguments[2],v=new Me(g,d),e=new qe(this.getRoot(),v,p);return this.nearestNeighbour(e)[0]}},interfaces_:function(){return[Fe,u]},getClass:function(){return ke}}),ke.centreX=function(t){return ke.avg(t.getMinX(),t.getMaxX())},ke.avg=function(t,e){return(t+e)/2},ke.centreY=function(t){return ke.avg(t.getMinY(),t.getMaxY())},h(Ye,Ge),e(Ye.prototype,{computeBounds:function(){for(var t=null,e=this.getChildBoundables().iterator();e.hasNext();){var n=e.next();null===t?t=new C(n.getBounds()):t.expandToInclude(n.getBounds())}return t},interfaces_:function(){return[]},getClass:function(){return Ye}}),ke.STRtreeNode=Ye,ke.serialVersionUID=0x39920f7d5f261e0,ke.xComparator={interfaces_:function(){return[a]},compare:function(t,e){return Be.compareDoubles(ke.centreX(t.getBounds()),ke.centreX(e.getBounds()))}},ke.yComparator={interfaces_:function(){return[a]},compare:function(t,e){return Be.compareDoubles(ke.centreY(t.getBounds()),ke.centreY(e.getBounds()))}},ke.intersectsOp={interfaces_:function(){return[IntersectsOp]},intersects:function(t,e){return t.intersects(e)}},ke.DEFAULT_NODE_CAPACITY=10,e(Ue.prototype,{interfaces_:function(){return[]},getClass:function(){return Ue}}),Ue.relativeSign=function(t,e){return e>t?-1:t>e?1:0},Ue.compare=function(t,e,n){if(e.equals2D(n))return 0;var i=Ue.relativeSign(e.x,n.x),r=Ue.relativeSign(e.y,n.y);switch(t){case 0:return Ue.compareValue(i,r);case 1:return Ue.compareValue(r,i);case 2:return Ue.compareValue(r,-i);case 3:return Ue.compareValue(-i,r);case 4:return Ue.compareValue(-i,-r);case 5:return Ue.compareValue(-r,-i);case 6:return Ue.compareValue(-r,i);case 7:return Ue.compareValue(i,-r)}return f.shouldNeverReachHere("invalid octant value"),0},Ue.compareValue=function(t,e){return 0>t?-1:t>0?1:0>e?-1:e>0?1:0},e(Xe.prototype,{getCoordinate:function(){return this.coord},print:function(t){t.print(this.coord),t.print(" seg # = "+this.segmentIndex)},compareTo:function(t){var e=t;return this.segmentIndex<e.segmentIndex?-1:this.segmentIndex>e.segmentIndex?1:this.coord.equals2D(e.coord)?0:Ue.compare(this.segmentOctant,this.coord,e.coord)},isEndPoint:function(t){return 0!==this.segmentIndex||this._isInterior?this.segmentIndex===t:!0},isInterior:function(){return this._isInterior},interfaces_:function(){return[s]},getClass:function(){return Xe}}),e(He.prototype,{getSplitCoordinates:function(){var t=new N;this.addEndpoints();for(var e=this.iterator(),n=e.next();e.hasNext();){var i=e.next();this.addEdgeCoordinates(n,i,t),n=i}return t.toCoordinateArray()},addCollapsedNodes:function(){var t=new I;this.findCollapsesFromInsertedNodes(t),this.findCollapsesFromExistingVertices(t);for(var e=t.iterator();e.hasNext();){var n=e.next().intValue();this.add(this.edge.getCoordinate(n),n)}},print:function(t){t.println("Intersections:");for(var e=this.iterator();e.hasNext();){var n=e.next();n.print(t)}},findCollapsesFromExistingVertices:function(t){for(var e=0;e<this.edge.size()-2;e++){var n=this.edge.getCoordinate(e),i=(this.edge.getCoordinate(e+1),this.edge.getCoordinate(e+2));n.equals2D(i)&&t.add(new b(e+1))}},addEdgeCoordinates:function(t,e,n){var i=e.segmentIndex-t.segmentIndex+2,r=this.edge.getCoordinate(e.segmentIndex),s=e.isInterior()||!e.coord.equals2D(r);s||i--;n.add(new g(t.coord),!1);for(var o=t.segmentIndex+1;o<=e.segmentIndex;o++)n.add(this.edge.getCoordinate(o));s&&n.add(new g(e.coord))},iterator:function(){return this.nodeMap.values().iterator()},addSplitEdges:function(t){this.addEndpoints(),this.addCollapsedNodes();for(var e=this.iterator(),n=e.next();e.hasNext();){var i=e.next(),r=this.createSplitEdge(n,i);t.add(r),n=i}},findCollapseIndex:function(t,e,n){if(!t.coord.equals2D(e.coord))return!1;var i=e.segmentIndex-t.segmentIndex;return e.isInterior()||i--,1===i?(n[0]=t.segmentIndex+1,!0):!1},findCollapsesFromInsertedNodes:function(t){for(var e=new Array(1).fill(null),n=this.iterator(),i=n.next();n.hasNext();){var r=n.next(),s=this.findCollapseIndex(i,r,e);s&&t.add(new b(e[0])),i=r}},getEdge:function(){return this.edge},addEndpoints:function(){var t=this.edge.size()-1;this.add(this.edge.getCoordinate(0),0),this.add(this.edge.getCoordinate(t),t)},createSplitEdge:function(t,e){var n=e.segmentIndex-t.segmentIndex+2,i=this.edge.getCoordinate(e.segmentIndex),r=e.isInterior()||!e.coord.equals2D(i);r||n--;var s=new Array(n).fill(null),o=0;s[o++]=new g(t.coord);for(var a=t.segmentIndex+1;a<=e.segmentIndex;a++)s[o++]=this.edge.getCoordinate(a);return r&&(s[o]=new g(e.coord)),new Ke(s,this.edge.getData())},add:function(t,e){var n=new Xe(this.edge,t,e,this.edge.getSegmentOctant(e)),i=this.nodeMap.get(n);return null!==i?(f.isTrue(i.coord.equals2D(t),"Found equal nodes with different coordinates"),i):(this.nodeMap.put(n,n),n)},checkSplitEdgesCorrectness:function(t){var e=this.edge.getCoordinates(),n=t.get(0),i=n.getCoordinate(0);if(!i.equals2D(e[0]))throw new l("bad split edge start point at "+i);var r=t.get(t.size()-1),s=r.getCoordinates(),o=s[s.length-1];if(!o.equals2D(e[e.length-1]))throw new l("bad split edge end point at "+o)},interfaces_:function(){return[]},getClass:function(){return He}}),e(We.prototype,{next:function(){return null===this.currNode?(this.currNode=this.nextNode,this.currSegIndex=this.currNode.segmentIndex,this.readNextNode(),this.currNode):null===this.nextNode?null:this.nextNode.segmentIndex===this.currNode.segmentIndex?(this.currNode=this.nextNode,this.currSegIndex=this.currNode.segmentIndex,this.readNextNode(),this.currNode):(this.nextNode.segmentIndex>this.currNode.segmentIndex,null)},remove:function(){throw new UnsupportedOperationException(this.getClass().getName())},hasNext:function(){return null!==this.nextNode},readNextNode:function(){this.nodeIt.hasNext()?this.nextNode=this.nodeIt.next():this.nextNode=null},interfaces_:function(){return[p]},getClass:function(){return We}}),e(je.prototype,{addIntersection:function(t,e){},interfaces_:function(){return[be]},getClass:function(){return je}}),e(Ke.prototype,{getCoordinates:function(){return this.pts},size:function(){return this.pts.length},getCoordinate:function(t){return this.pts[t]},isClosed:function(){return this.pts[0].equals(this.pts[this.pts.length-1])},getSegmentOctant:function(t){return t===this.pts.length-1?-1:this.safeOctant(this.getCoordinate(t),this.getCoordinate(t+1))},setData:function(t){this.data=t},safeOctant:function(t,e){return t.equals2D(e)?0:Pe.octant(t,e)},getData:function(){return this.data},addIntersection:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];this.addIntersectionNode(t,e)}else if(4===arguments.length){var n=arguments[0],i=arguments[1],r=(arguments[2],arguments[3]),s=new g(n.getIntersection(r));this.addIntersection(s,i)}},toString:function(){return se.toLineString(new Gt(this.pts))},getNodeList:function(){return this.nodeList},addIntersectionNode:function(t,e){var n=e,i=n+1;if(i<this.pts.length){var r=this.pts[i];t.equals2D(r)&&(n=i)}var s=this.nodeList.add(t,n);return s},addIntersections:function(t,e,n){for(var i=0;i<t.getIntersectionNum();i++)this.addIntersection(t,e,n,i)},interfaces_:function(){return[je]},getClass:function(){return Ke}}),Ke.getNodedSubstrings=function(){if(1===arguments.length){var t=arguments[0],e=new I;return Ke.getNodedSubstrings(t,e),e}if(2===arguments.length)for(var n=arguments[0],i=arguments[1],r=n.iterator();r.hasNext();){var s=r.next();s.getNodeList().addSplitEdges(i)}},e(Ze.prototype,{overlap:function(){if(2===arguments.length){arguments[0],arguments[1]}else if(4===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];t.getLineSegment(e,this.overlapSeg1),n.getLineSegment(i,this.overlapSeg2),this.overlap(this.overlapSeg1,this.overlapSeg2)}},interfaces_:function(){return[]},getClass:function(){return Ze}}),
e(Qe.prototype,{getLineSegment:function(t,e){e.p0=this.pts[t],e.p1=this.pts[t+1]},computeSelect:function(t,e,n,i){var r=this.pts[e],s=this.pts[n];if(i.tempEnv1.init(r,s),n-e===1)return i.select(this,e),null;if(!t.intersects(i.tempEnv1))return null;var o=Math.trunc((e+n)/2);o>e&&this.computeSelect(t,e,o,i),n>o&&this.computeSelect(t,o,n,i)},getCoordinates:function(){for(var t=new Array(this.end-this.start+1).fill(null),e=0,n=this.start;n<=this.end;n++)t[e++]=this.pts[n];return t},computeOverlaps:function(t,e){this.computeOverlapsInternal(this.start,this.end,t,t.start,t.end,e)},setId:function(t){this.id=t},select:function(t,e){this.computeSelect(t,this.start,this.end,e)},getEnvelope:function(){if(null===this.env){var t=this.pts[this.start],e=this.pts[this.end];this.env=new C(t,e)}return this.env},getEndIndex:function(){return this.end},getStartIndex:function(){return this.start},getContext:function(){return this.context},getId:function(){return this.id},computeOverlapsInternal:function(t,e,n,i,r,s){var o=this.pts[t],a=this.pts[e],u=n.pts[i],l=n.pts[r];if(e-t===1&&r-i===1)return s.overlap(this,t,n,i),null;if(s.tempEnv1.init(o,a),s.tempEnv2.init(u,l),!s.tempEnv1.intersects(s.tempEnv2))return null;var h=Math.trunc((t+e)/2),c=Math.trunc((i+r)/2);h>t&&(c>i&&this.computeOverlapsInternal(t,h,n,i,c,s),r>c&&this.computeOverlapsInternal(t,h,n,c,r,s)),e>h&&(c>i&&this.computeOverlapsInternal(h,e,n,i,c,s),r>c&&this.computeOverlapsInternal(h,e,n,c,r,s))},interfaces_:function(){return[]},getClass:function(){return Qe}}),e(Je.prototype,{interfaces_:function(){return[]},getClass:function(){return Je}}),Je.isNorthern=function(t){return t===Je.NE||t===Je.NW},Je.isOpposite=function(t,e){if(t===e)return!1;var n=(t-e+4)%4;return 2===n},Je.commonHalfPlane=function(t,e){if(t===e)return t;var n=(t-e+4)%4;if(2===n)return-1;var i=e>t?t:e,r=t>e?t:e;return 0===i&&3===r?3:i},Je.isInHalfPlane=function(t,e){return e===Je.SE?t===Je.SE||t===Je.SW:t===e||t===e+1},Je.quadrant=function(){if("number"==typeof arguments[0]&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1];if(0===t&&0===e)throw new i("Cannot compute the quadrant for point ( "+t+", "+e+" )");return t>=0?e>=0?Je.NE:Je.SE:e>=0?Je.NW:Je.SW}if(arguments[0]instanceof g&&arguments[1]instanceof g){var n=arguments[0],r=arguments[1];if(r.x===n.x&&r.y===n.y)throw new i("Cannot compute the quadrant for two identical points "+n);return r.x>=n.x?r.y>=n.y?Je.NE:Je.SE:r.y>=n.y?Je.NW:Je.SW}},Je.NE=0,Je.NW=1,Je.SW=2,Je.SE=3,e($e.prototype,{interfaces_:function(){return[]},getClass:function(){return $e}}),$e.getChainStartIndices=function(t){var e=0,n=new I;n.add(new b(e));do{var i=$e.findChainEnd(t,e);n.add(new b(i)),e=i}while(e<t.length-1);var r=$e.toIntArray(n);return r},$e.findChainEnd=function(t,e){for(var n=e;n<t.length-1&&t[n].equals2D(t[n+1]);)n++;if(n>=t.length-1)return t.length-1;for(var i=Je.quadrant(t[n],t[n+1]),r=e+1;r<t.length;){if(!t[r-1].equals2D(t[r])){var s=Je.quadrant(t[r-1],t[r]);if(s!==i)break}r++}return r-1},$e.getChains=function(){if(1===arguments.length){var t=arguments[0];return $e.getChains(t,null)}if(2===arguments.length){for(var e=arguments[0],n=arguments[1],i=new I,r=$e.getChainStartIndices(e),s=0;s<r.length-1;s++){var o=new Qe(e,r[s],r[s+1],n);i.add(o)}return i}},$e.toIntArray=function(t){for(var e=new Array(t.size()).fill(null),n=0;n<e.length;n++)e[n]=t.get(n).intValue();return e},e(tn.prototype,{computeNodes:function(t){},getNodedSubstrings:function(){},interfaces_:function(){return[]},getClass:function(){return tn}}),e(en.prototype,{setSegmentIntersector:function(t){this.segInt=t},interfaces_:function(){return[tn]},getClass:function(){return en}}),h(nn,en),e(nn.prototype,{getMonotoneChains:function(){return this.monoChains},getNodedSubstrings:function(){return Ke.getNodedSubstrings(this.nodedSegStrings)},getIndex:function(){return this.index},add:function(t){for(var e=$e.getChains(t.getCoordinates(),t),n=e.iterator();n.hasNext();){var i=n.next();i.setId(this.idCounter++),this.index.insert(i.getEnvelope(),i),this.monoChains.add(i)}},computeNodes:function(t){this.nodedSegStrings=t;for(var e=t.iterator();e.hasNext();)this.add(e.next());this.intersectChains()},intersectChains:function(){for(var t=new rn(this.segInt),e=this.monoChains.iterator();e.hasNext();)for(var n=e.next(),i=this.index.query(n.getEnvelope()),r=i.iterator();r.hasNext();){var s=r.next();if(s.getId()>n.getId()&&(n.computeOverlaps(s,t),this.nOverlaps++),this.segInt.isDone())return null}},interfaces_:function(){return[]},getClass:function(){return nn}}),h(rn,Ze),e(rn.prototype,{overlap:function(){if(4!==arguments.length)return Ze.prototype.overlap.apply(this,arguments);var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3],r=t.getContext(),s=n.getContext();this.si.processIntersections(r,e,s,i)},interfaces_:function(){return[]},getClass:function(){return rn}}),nn.SegmentOverlapAction=rn,h(sn,l),e(sn.prototype,{getCoordinate:function(){return this.pt},interfaces_:function(){return[]},getClass:function(){return sn}}),sn.msgWithCoord=function(t,e){return null!==e?t+" [ "+e+" ]":t},e(on.prototype,{processIntersections:function(t,e,n,i){},isDone:function(){},interfaces_:function(){return[]},getClass:function(){return on}}),e(an.prototype,{getInteriorIntersection:function(){return this.interiorIntersection},setCheckEndSegmentsOnly:function(t){this.isCheckEndSegmentsOnly=t},getIntersectionSegments:function(){return this.intSegments},count:function(){return this.intersectionCount},getIntersections:function(){return this.intersections},setFindAllIntersections:function(t){this.findAllIntersections=t},setKeepIntersections:function(t){this.keepIntersections=t},processIntersections:function(t,e,n,i){if(!this.findAllIntersections&&this.hasIntersection())return null;if(t===n&&e===i)return null;if(this.isCheckEndSegmentsOnly){var r=this.isEndSegment(t,e)||this.isEndSegment(n,i);if(!r)return null}var s=t.getCoordinates()[e],o=t.getCoordinates()[e+1],a=n.getCoordinates()[i],u=n.getCoordinates()[i+1];this.li.computeIntersection(s,o,a,u),this.li.hasIntersection()&&this.li.isInteriorIntersection()&&(this.intSegments=new Array(4).fill(null),this.intSegments[0]=s,this.intSegments[1]=o,this.intSegments[2]=a,this.intSegments[3]=u,this.interiorIntersection=this.li.getIntersection(0),this.keepIntersections&&this.intersections.add(this.interiorIntersection),this.intersectionCount++)},isEndSegment:function(t,e){return 0===e?!0:e>=t.size()-2},hasIntersection:function(){return null!==this.interiorIntersection},isDone:function(){return this.findAllIntersections?!1:null!==this.interiorIntersection},interfaces_:function(){return[on]},getClass:function(){return an}}),an.createAllIntersectionsFinder=function(t){var e=new an(t);return e.setFindAllIntersections(!0),e},an.createAnyIntersectionFinder=function(t){return new an(t)},an.createIntersectionCounter=function(t){var e=new an(t);return e.setFindAllIntersections(!0),e.setKeepIntersections(!1),e},e(un.prototype,{execute:function(){return null!==this.segInt?null:void this.checkInteriorIntersections()},getIntersections:function(){return this.segInt.getIntersections()},isValid:function(){return this.execute(),this._isValid},setFindAllIntersections:function(t){this.findAllIntersections=t},checkInteriorIntersections:function(){this._isValid=!0,this.segInt=new an(this.li),this.segInt.setFindAllIntersections(this.findAllIntersections);var t=new nn;return t.setSegmentIntersector(this.segInt),t.computeNodes(this.segStrings),this.segInt.hasIntersection()?(this._isValid=!1,null):void 0},checkValid:function(){if(this.execute(),!this._isValid)throw new sn(this.getErrorMessage(),this.segInt.getInteriorIntersection())},getErrorMessage:function(){if(this._isValid)return"no intersections found";var t=this.segInt.getIntersectionSegments();return"found non-noded intersection between "+se.toLineString(t[0],t[1])+" and "+se.toLineString(t[2],t[3])},interfaces_:function(){return[]},getClass:function(){return un}}),un.computeIntersections=function(t){var e=new un(t);return e.setFindAllIntersections(!0),e.isValid(),e.getIntersections()},e(ln.prototype,{checkValid:function(){this.nv.checkValid()},interfaces_:function(){return[]},getClass:function(){return ln}}),ln.toSegmentStrings=function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next();e.add(new Oe(i.getCoordinates(),i))}return e},ln.checkValid=function(t){var e=new ln(t);e.checkValid()},e(hn.prototype,{map:function(t){for(var e=new I,n=0;n<t.getNumGeometries();n++){var i=this.mapOp.map(t.getGeometryN(n));i.isEmpty()||e.add(i)}return t.getFactory().createGeometryCollection(ie.toGeometryArray(e))},interfaces_:function(){return[]},getClass:function(){return hn}}),hn.map=function(t,e){var n=new hn(e);return n.map(t)},e(cn.prototype,{interfaces_:function(){return[]},getClass:function(){return cn}}),cn.opposite=function(t){return t===cn.LEFT?cn.RIGHT:t===cn.RIGHT?cn.LEFT:t},cn.ON=0,cn.LEFT=1,cn.RIGHT=2,e(fn.prototype,{setAllLocations:function(t){for(var e=0;e<this.location.length;e++)this.location[e]=t},isNull:function(){for(var t=0;t<this.location.length;t++)if(this.location[t]!==L.NONE)return!1;return!0},setAllLocationsIfNull:function(t){for(var e=0;e<this.location.length;e++)this.location[e]===L.NONE&&(this.location[e]=t)},isLine:function(){return 1===this.location.length},merge:function(t){if(t.location.length>this.location.length){var e=new Array(3).fill(null);e[cn.ON]=this.location[cn.ON],e[cn.LEFT]=L.NONE,e[cn.RIGHT]=L.NONE,this.location=e}for(var n=0;n<this.location.length;n++)this.location[n]===L.NONE&&n<t.location.length&&(this.location[n]=t.location[n])},getLocations:function(){return this.location},flip:function(){if(this.location.length<=1)return null;var t=this.location[cn.LEFT];this.location[cn.LEFT]=this.location[cn.RIGHT],this.location[cn.RIGHT]=t},toString:function(){var t=new P;return this.location.length>1&&t.append(L.toLocationSymbol(this.location[cn.LEFT])),t.append(L.toLocationSymbol(this.location[cn.ON])),this.location.length>1&&t.append(L.toLocationSymbol(this.location[cn.RIGHT])),t.toString()},setLocations:function(t,e,n){this.location[cn.ON]=t,this.location[cn.LEFT]=e,this.location[cn.RIGHT]=n},get:function(t){return t<this.location.length?this.location[t]:L.NONE},isArea:function(){return this.location.length>1},isAnyNull:function(){for(var t=0;t<this.location.length;t++)if(this.location[t]===L.NONE)return!0;return!1},setLocation:function(){if(1===arguments.length){var t=arguments[0];this.setLocation(cn.ON,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.location[e]=n}},init:function(t){this.location=new Array(t).fill(null),this.setAllLocations(L.NONE)},isEqualOnSide:function(t,e){return this.location[e]===t.location[e]},allPositionsEqual:function(t){for(var e=0;e<this.location.length;e++)if(this.location[e]!==t)return!1;return!0},interfaces_:function(){return[]},getClass:function(){return fn}}),e(gn.prototype,{getGeometryCount:function(){var t=0;return this.elt[0].isNull()||t++,this.elt[1].isNull()||t++,t},setAllLocations:function(t,e){this.elt[t].setAllLocations(e)},isNull:function(t){return this.elt[t].isNull()},setAllLocationsIfNull:function(){if(1===arguments.length){var t=arguments[0];this.setAllLocationsIfNull(0,t),this.setAllLocationsIfNull(1,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.elt[e].setAllLocationsIfNull(n)}},isLine:function(t){return this.elt[t].isLine()},merge:function(t){for(var e=0;2>e;e++)null===this.elt[e]&&null!==t.elt[e]?this.elt[e]=new fn(t.elt[e]):this.elt[e].merge(t.elt[e])},flip:function(){this.elt[0].flip(),this.elt[1].flip()},getLocation:function(){if(1===arguments.length){var t=arguments[0];return this.elt[t].get(cn.ON)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return this.elt[e].get(n)}},toString:function(){var t=new P;return null!==this.elt[0]&&(t.append("A:"),t.append(this.elt[0].toString())),null!==this.elt[1]&&(t.append(" B:"),t.append(this.elt[1].toString())),t.toString()},isArea:function(){if(0===arguments.length)return this.elt[0].isArea()||this.elt[1].isArea();if(1===arguments.length){var t=arguments[0];return this.elt[t].isArea()}},isAnyNull:function(t){return this.elt[t].isAnyNull()},setLocation:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];this.elt[t].setLocation(cn.ON,e)}else if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];this.elt[n].setLocation(i,r)}},isEqualOnSide:function(t,e){return this.elt[0].isEqualOnSide(t.elt[0],e)&&this.elt[1].isEqualOnSide(t.elt[1],e)},allPositionsEqual:function(t,e){return this.elt[t].allPositionsEqual(e)},toLine:function(t){this.elt[t].isArea()&&(this.elt[t]=new fn(this.elt[t].location[0]))},interfaces_:function(){return[]},getClass:function(){return gn}}),gn.toLineLabel=function(t){for(var e=new gn(L.NONE),n=0;2>n;n++)e.setLocation(n,t.getLocation(n));return e},e(dn.prototype,{computeRing:function(){if(null!==this.ring)return null;for(var t=new Array(this.pts.size()).fill(null),e=0;e<this.pts.size();e++)t[e]=this.pts.get(e);this.ring=this.geometryFactory.createLinearRing(t),this._isHole=he.isCCW(this.ring.getCoordinates())},isIsolated:function(){return 1===this.label.getGeometryCount()},computePoints:function(t){this.startDe=t;var e=t,n=!0;do{if(null===e)throw new sn("Found null DirectedEdge");if(e.getEdgeRing()===this)throw new sn("Directed Edge visited twice during ring-building at "+e.getCoordinate());this.edges.add(e);var i=e.getLabel();f.isTrue(i.isArea()),this.mergeLabel(i),this.addPoints(e.getEdge(),e.isForward(),n),n=!1,this.setEdgeRing(e,this),e=this.getNext(e)}while(e!==this.startDe)},getLinearRing:function(){return this.ring},getCoordinate:function(t){return this.pts.get(t)},computeMaxNodeDegree:function(){this.maxNodeDegree=0;var t=this.startDe;do{var e=t.getNode(),n=e.getEdges().getOutgoingDegree(this);n>this.maxNodeDegree&&(this.maxNodeDegree=n),t=this.getNext(t)}while(t!==this.startDe);this.maxNodeDegree*=2},addPoints:function(t,e,n){var i=t.getCoordinates();if(e){var r=1;n&&(r=0);for(var s=r;s<i.length;s++)this.pts.add(i[s])}else{var r=i.length-2;n&&(r=i.length-1);for(var s=r;s>=0;s--)this.pts.add(i[s])}},isHole:function(){return this._isHole},setInResult:function(){var t=this.startDe;do t.getEdge().setInResult(!0),t=t.getNext();while(t!==this.startDe)},containsPoint:function(t){var e=this.getLinearRing(),n=e.getEnvelopeInternal();if(!n.contains(t))return!1;if(!he.isPointInRing(t,e.getCoordinates()))return!1;for(var i=this.holes.iterator();i.hasNext();){var r=i.next();if(r.containsPoint(t))return!1}return!0},addHole:function(t){this.holes.add(t)},isShell:function(){return null===this.shell},getLabel:function(){return this.label},getEdges:function(){return this.edges},getMaxNodeDegree:function(){return this.maxNodeDegree<0&&this.computeMaxNodeDegree(),this.maxNodeDegree},getShell:function(){return this.shell},mergeLabel:function(){if(1===arguments.length){var t=arguments[0];this.mergeLabel(t,0),this.mergeLabel(t,1)}else if(2===arguments.length){var e=arguments[0],n=arguments[1],i=e.getLocation(n,cn.RIGHT);if(i===L.NONE)return null;if(this.label.getLocation(n)===L.NONE)return this.label.setLocation(n,i),null}},setShell:function(t){this.shell=t,null!==t&&t.addHole(this)},toPolygon:function(t){for(var e=new Array(this.holes.size()).fill(null),n=0;n<this.holes.size();n++)e[n]=this.holes.get(n).getLinearRing();var i=t.createPolygon(this.getLinearRing(),e);return i},interfaces_:function(){return[]},getClass:function(){return dn}}),h(pn,dn),e(pn.prototype,{setEdgeRing:function(t,e){t.setMinEdgeRing(e)},getNext:function(t){return t.getNextMin()},interfaces_:function(){return[]},getClass:function(){return pn}}),h(vn,dn),e(vn.prototype,{buildMinimalRings:function(){var t=new I,e=this.startDe;do{if(null===e.getMinEdgeRing()){var n=new pn(e,this.geometryFactory);t.add(n)}e=e.getNext()}while(e!==this.startDe);return t},setEdgeRing:function(t,e){t.setEdgeRing(e)},linkDirectedEdgesForMinimalEdgeRings:function(){var t=this.startDe;do{var e=t.getNode();e.getEdges().linkMinimalDirectedEdges(this),t=t.getNext()}while(t!==this.startDe)},getNext:function(t){return t.getNext()},interfaces_:function(){return[]},getClass:function(){return vn}}),e(mn.prototype,{setVisited:function(t){this._isVisited=t},setInResult:function(t){this._isInResult=t},isCovered:function(){return this._isCovered},isCoveredSet:function(){return this._isCoveredSet},setLabel:function(t){this.label=t},getLabel:function(){return this.label},setCovered:function(t){this._isCovered=t,this._isCoveredSet=!0},updateIM:function(t){f.isTrue(this.label.getGeometryCount()>=2,"found partial label"),this.computeIM(t)},isInResult:function(){return this._isInResult},isVisited:function(){return this._isVisited},interfaces_:function(){return[]},getClass:function(){return mn}}),h(yn,mn),e(yn.prototype,{isIncidentEdgeInResult:function(){for(var t=this.getEdges().getEdges().iterator();t.hasNext();){var e=t.next();if(e.getEdge().isInResult())return!0}return!1},isIsolated:function(){return 1===this.label.getGeometryCount()},getCoordinate:function(){return this.coord},print:function(t){t.println("node "+this.coord+" lbl: "+this.label)},computeIM:function(t){},computeMergedLocation:function(t,e){var n=L.NONE;if(n=this.label.getLocation(e),!t.isNull(e)){var i=t.getLocation(e);n!==L.BOUNDARY&&(n=i)}return n},setLabel:function(){if(2!==arguments.length)return mn.prototype.setLabel.apply(this,arguments);var t=arguments[0],e=arguments[1];null===this.label?this.label=new gn(t,e):this.label.setLocation(t,e)},getEdges:function(){return this.edges},mergeLabel:function(){if(arguments[0]instanceof yn){var t=arguments[0];this.mergeLabel(t.label)}else if(arguments[0]instanceof gn)for(var e=arguments[0],n=0;2>n;n++){var i=this.computeMergedLocation(e,n),r=this.label.getLocation(n);r===L.NONE&&this.label.setLocation(n,i)}},add:function(t){this.edges.insert(t),t.setNode(this)},setLabelBoundary:function(t){if(null===this.label)return null;var e=L.NONE;null!==this.label&&(e=this.label.getLocation(t));var n=null;switch(e){case L.BOUNDARY:n=L.INTERIOR;break;case L.INTERIOR:n=L.BOUNDARY;break;default:n=L.BOUNDARY}this.label.setLocation(t,n)},interfaces_:function(){return[]},getClass:function(){return yn}}),e(xn.prototype,{find:function(t){return this.nodeMap.get(t)},addNode:function(){if(arguments[0]instanceof g){var t=arguments[0],e=this.nodeMap.get(t);return null===e&&(e=this.nodeFact.createNode(t),this.nodeMap.put(t,e)),e}if(arguments[0]instanceof yn){var n=arguments[0],e=this.nodeMap.get(n.getCoordinate());return null===e?(this.nodeMap.put(n.getCoordinate(),n),n):(e.mergeLabel(n),e)}},print:function(t){for(var e=this.iterator();e.hasNext();){var n=e.next();n.print(t)}},iterator:function(){return this.nodeMap.values().iterator()},values:function(){return this.nodeMap.values()},getBoundaryNodes:function(t){for(var e=new I,n=this.iterator();n.hasNext();){var i=n.next();i.getLabel().getLocation(t)===L.BOUNDARY&&e.add(i)}return e},add:function(t){var e=t.getCoordinate(),n=this.addNode(e);n.add(t)},interfaces_:function(){return[]},getClass:function(){return xn}}),e(En.prototype,{compareDirection:function(t){return this.dx===t.dx&&this.dy===t.dy?0:this.quadrant>t.quadrant?1:this.quadrant<t.quadrant?-1:he.computeOrientation(t.p0,t.p1,this.p1)},getDy:function(){return this.dy},getCoordinate:function(){return this.p0},setNode:function(t){this.node=t},print:function(t){var e=Math.atan2(this.dy,this.dx),n=this.getClass().getName(),i=n.lastIndexOf("."),r=n.substring(i+1);t.print("  "+r+": "+this.p0+" - "+this.p1+" "+this.quadrant+":"+e+"   "+this.label)},compareTo:function(t){var e=t;return this.compareDirection(e)},getDirectedCoordinate:function(){return this.p1},getDx:function(){return this.dx},getLabel:function(){return this.label},getEdge:function(){return this.edge},getQuadrant:function(){return this.quadrant},getNode:function(){return this.node},toString:function(){var t=Math.atan2(this.dy,this.dx),e=this.getClass().getName(),n=e.lastIndexOf("."),i=e.substring(n+1);return"  "+i+": "+this.p0+" - "+this.p1+" "+this.quadrant+":"+t+"   "+this.label},computeLabel:function(t){},init:function(t,e){this.p0=t,this.p1=e,this.dx=e.x-t.x,this.dy=e.y-t.y,this.quadrant=Je.quadrant(this.dx,this.dy),f.isTrue(!(0===this.dx&&0===this.dy),"EdgeEnd with identical endpoints found")},interfaces_:function(){return[s]},getClass:function(){return En}}),h(In,En),e(In.prototype,{getNextMin:function(){return this.nextMin},getDepth:function(t){return this.depth[t]},setVisited:function(t){this._isVisited=t},computeDirectedLabel:function(){this.label=new gn(this.edge.getLabel()),this._isForward||this.label.flip()},getNext:function(){return this.next},setDepth:function(t,e){if(-999!==this.depth[t]&&this.depth[t]!==e)throw new sn("assigned depths do not match",this.getCoordinate());this.depth[t]=e},isInteriorAreaEdge:function Bo(){for(var Bo=!0,t=0;2>t;t++)this.label.isArea(t)&&this.label.getLocation(t,cn.LEFT)===L.INTERIOR&&this.label.getLocation(t,cn.RIGHT)===L.INTERIOR||(Bo=!1);return Bo},setNextMin:function(t){this.nextMin=t},print:function(t){En.prototype.print.call(this,t),t.print(" "+this.depth[cn.LEFT]+"/"+this.depth[cn.RIGHT]),t.print(" ("+this.getDepthDelta()+")"),this._isInResult&&t.print(" inResult")},setMinEdgeRing:function(t){this.minEdgeRing=t},isLineEdge:function(){var t=this.label.isLine(0)||this.label.isLine(1),e=!this.label.isArea(0)||this.label.allPositionsEqual(0,L.EXTERIOR),n=!this.label.isArea(1)||this.label.allPositionsEqual(1,L.EXTERIOR);return t&&e&&n},setEdgeRing:function(t){this.edgeRing=t},getMinEdgeRing:function(){return this.minEdgeRing},getDepthDelta:function(){var t=this.edge.getDepthDelta();return this._isForward||(t=-t),t},setInResult:function(t){this._isInResult=t},getSym:function(){return this.sym},isForward:function(){return this._isForward},getEdge:function(){return this.edge},printEdge:function(t){this.print(t),t.print(" "),this._isForward?this.edge.print(t):this.edge.printReverse(t)},setSym:function(t){this.sym=t},setVisitedEdge:function(t){this.setVisited(t),this.sym.setVisited(t)},setEdgeDepths:function(t,e){var n=this.getEdge().getDepthDelta();this._isForward||(n=-n);var i=1;t===cn.LEFT&&(i=-1);var r=cn.opposite(t),s=n*i,o=e+s;this.setDepth(t,e),this.setDepth(r,o)},getEdgeRing:function(){return this.edgeRing},isInResult:function(){return this._isInResult},setNext:function(t){this.next=t},isVisited:function(){return this._isVisited},interfaces_:function(){return[]},getClass:function(){return In}}),In.depthFactor=function(t,e){return t===L.EXTERIOR&&e===L.INTERIOR?1:t===L.INTERIOR&&e===L.EXTERIOR?-1:0},e(Nn.prototype,{createNode:function(t){return new yn(t,null)},interfaces_:function(){return[]},getClass:function(){return Nn}}),e(Cn.prototype,{printEdges:function(t){t.println("Edges:");for(var e=0;e<this.edges.size();e++){t.println("edge "+e+":");var n=this.edges.get(e);n.print(t),n.eiList.print(t)}},find:function(t){return this.nodes.find(t)},addNode:function(){if(arguments[0]instanceof yn){var t=arguments[0];return this.nodes.addNode(t)}if(arguments[0]instanceof g){var e=arguments[0];return this.nodes.addNode(e)}},getNodeIterator:function(){return this.nodes.iterator()},linkResultDirectedEdges:function(){for(var t=this.nodes.iterator();t.hasNext();){var e=t.next();e.getEdges().linkResultDirectedEdges()}},debugPrintln:function(t){A.out.println(t)},isBoundaryNode:function(t,e){var n=this.nodes.find(e);if(null===n)return!1;var i=n.getLabel();return null!==i&&i.getLocation(t)===L.BOUNDARY},linkAllDirectedEdges:function(){for(var t=this.nodes.iterator();t.hasNext();){var e=t.next();e.getEdges().linkAllDirectedEdges()}},matchInSameDirection:function(t,e,n,i){return t.equals(n)?he.computeOrientation(t,e,i)===he.COLLINEAR&&Je.quadrant(t,e)===Je.quadrant(n,i):!1},getEdgeEnds:function(){return this.edgeEndList},debugPrint:function(t){A.out.print(t)},getEdgeIterator:function(){return this.edges.iterator()},findEdgeInSameDirection:function(t,e){for(var n=0;n<this.edges.size();n++){var i=this.edges.get(n),r=i.getCoordinates();if(this.matchInSameDirection(t,e,r[0],r[1]))return i;if(this.matchInSameDirection(t,e,r[r.length-1],r[r.length-2]))return i}return null},insertEdge:function(t){this.edges.add(t)},findEdgeEnd:function(t){for(var e=this.getEdgeEnds().iterator();e.hasNext();){var n=e.next();if(n.getEdge()===t)return n}return null},addEdges:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.edges.add(n);var i=new In(n,!0),r=new In(n,!1);i.setSym(r),r.setSym(i),this.add(i),this.add(r)}},add:function(t){this.nodes.add(t),this.edgeEndList.add(t)},getNodes:function(){return this.nodes.values()},findEdge:function(t,e){for(var n=0;n<this.edges.size();n++){var i=this.edges.get(n),r=i.getCoordinates();if(t.equals(r[0])&&e.equals(r[1]))return i}return null},interfaces_:function(){return[]},getClass:function(){return Cn}}),Cn.linkResultDirectedEdges=function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();n.getEdges().linkResultDirectedEdges()}},e(Sn.prototype,{sortShellsAndHoles:function(t,e,n){for(var i=t.iterator();i.hasNext();){var r=i.next();r.isHole()?n.add(r):e.add(r)}},computePolygons:function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next(),r=i.toPolygon(this.geometryFactory);e.add(r)}return e},placeFreeHoles:function(t,e){for(var n=e.iterator();n.hasNext();){var i=n.next();if(null===i.getShell()){var r=this.findEdgeRingContaining(i,t);if(null===r)throw new sn("unable to assign hole to a shell",i.getCoordinate(0));i.setShell(r)}}},buildMinimalEdgeRings:function(t,e,n){for(var i=new I,r=t.iterator();r.hasNext();){var s=r.next();if(s.getMaxNodeDegree()>2){s.linkDirectedEdgesForMinimalEdgeRings();var o=s.buildMinimalRings(),a=this.findShell(o);null!==a?(this.placePolygonHoles(a,o),e.add(a)):n.addAll(o)}else i.add(s)}return i},containsPoint:function(t){for(var e=this.shellList.iterator();e.hasNext();){var n=e.next();if(n.containsPoint(t))return!0}return!1},buildMaximalEdgeRings:function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next();if(i.isInResult()&&i.getLabel().isArea()&&null===i.getEdgeRing()){var r=new vn(i,this.geometryFactory);e.add(r),r.setInResult()}}return e},placePolygonHoles:function(t,e){for(var n=e.iterator();n.hasNext();){var i=n.next();i.isHole()&&i.setShell(t)}},getPolygons:function(){var t=this.computePolygons(this.shellList);return t},findEdgeRingContaining:function(t,e){for(var n=t.getLinearRing(),i=n.getEnvelopeInternal(),r=n.getCoordinateN(0),s=null,o=null,a=e.iterator();a.hasNext();){var u=a.next(),l=u.getLinearRing(),h=l.getEnvelopeInternal();null!==s&&(o=s.getLinearRing().getEnvelopeInternal());var c=!1;h.contains(i)&&he.isPointInRing(r,l.getCoordinates())&&(c=!0),c&&(null===s||o.contains(h))&&(s=u)}return s},findShell:function(t){for(var e=0,n=null,i=t.iterator();i.hasNext();){var r=i.next();r.isHole()||(n=r,e++)}return f.isTrue(1>=e,"found two shells in MinimalEdgeRing list"),n},add:function(){if(1===arguments.length){var t=arguments[0];this.add(t.getEdgeEnds(),t.getNodes())}else if(2===arguments.length){var e=arguments[0],n=arguments[1];Cn.linkResultDirectedEdges(n);var i=this.buildMaximalEdgeRings(e),r=new I,s=this.buildMinimalEdgeRings(i,this.shellList,r);this.sortShellsAndHoles(s,this.shellList,r),this.placeFreeHoles(this.shellList,r)}},interfaces_:function(){return[]},getClass:function(){return Sn}}),e(wn.prototype,{collectLines:function(t){for(var e=this.op.getGraph().getEdgeEnds().iterator();e.hasNext();){var n=e.next();this.collectLineEdge(n,t,this.lineEdgesList),this.collectBoundaryTouchEdge(n,t,this.lineEdgesList)}},labelIsolatedLine:function(t,e){var n=this.ptLocator.locate(t.getCoordinate(),this.op.getArgGeometry(e));t.getLabel().setLocation(e,n)},build:function(t){return this.findCoveredLineEdges(),this.collectLines(t),this.buildLines(t),this.resultLineList},collectLineEdge:function(t,e,n){var i=t.getLabel(),r=t.getEdge();t.isLineEdge()&&(t.isVisited()||!ii.isResultOfOp(i,e)||r.isCovered()||(n.add(r),t.setVisitedEdge(!0)))},findCoveredLineEdges:function(){for(var t=this.op.getGraph().getNodes().iterator();t.hasNext();){var e=t.next();e.getEdges().findCoveredLineEdges()}for(var n=this.op.getGraph().getEdgeEnds().iterator();n.hasNext();){var i=n.next(),r=i.getEdge();if(i.isLineEdge()&&!r.isCoveredSet()){var s=this.op.isCoveredByA(i.getCoordinate());r.setCovered(s)}}},labelIsolatedLines:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next(),i=n.getLabel();n.isIsolated()&&(i.isNull(0)?this.labelIsolatedLine(n,0):this.labelIsolatedLine(n,1))}},buildLines:function(t){for(var e=this.lineEdgesList.iterator();e.hasNext();){var n=e.next(),i=(n.getLabel(),this.geometryFactory.createLineString(n.getCoordinates()));this.resultLineList.add(i),n.setInResult(!0)}},collectBoundaryTouchEdge:function(t,e,n){var i=t.getLabel();return t.isLineEdge()?null:t.isVisited()?null:t.isInteriorAreaEdge()?null:t.getEdge().isInResult()?null:(f.isTrue(!(t.isInResult()||t.getSym().isInResult())||!t.getEdge().isInResult()),void(ii.isResultOfOp(i,e)&&e===ii.INTERSECTION&&(n.add(t.getEdge()),t.setVisitedEdge(!0))))},interfaces_:function(){return[]},getClass:function(){return wn}}),e(Ln.prototype,{filterCoveredNodeToPoint:function(t){var e=t.getCoordinate();if(!this.op.isCoveredByLA(e)){var n=this.geometryFactory.createPoint(e);this.resultPointList.add(n)}},extractNonCoveredResultNodes:function(t){for(var e=this.op.getGraph().getNodes().iterator();e.hasNext();){var n=e.next();if(!(n.isInResult()||n.isIncidentEdgeInResult()||0!==n.getEdges().getDegree()&&t!==ii.INTERSECTION)){var i=n.getLabel();ii.isResultOfOp(i,t)&&this.filterCoveredNodeToPoint(n)}}},build:function(t){return this.extractNonCoveredResultNodes(t),this.resultPointList},interfaces_:function(){return[]},getClass:function(){return Ln}}),e(Rn.prototype,{locate:function(t){},interfaces_:function(){return[]},getClass:function(){return Rn}}),e(Tn.prototype,{locate:function(t){return Tn.locate(t,this.geom)},interfaces_:function(){return[Rn]},getClass:function(){return Tn}}),Tn.isPointInRing=function(t,e){return e.getEnvelopeInternal().intersects(t)?he.isPointInRing(t,e.getCoordinates()):!1},Tn.containsPointInPolygon=function(t,e){if(e.isEmpty())return!1;var n=e.getExteriorRing();if(!Tn.isPointInRing(t,n))return!1;for(var i=0;i<e.getNumInteriorRing();i++){var r=e.getInteriorRingN(i);if(Tn.isPointInRing(t,r))return!1}return!0},Tn.containsPoint=function(t,e){if(e instanceof Tt)return Tn.containsPointInPolygon(t,e);if(e instanceof ft)for(var n=new Re(e);n.hasNext();){var i=n.next();if(i!==e&&Tn.containsPoint(t,i))return!0}return!1},Tn.locate=function(t,e){return e.isEmpty()?L.EXTERIOR:Tn.containsPoint(t,e)?L.INTERIOR:L.EXTERIOR},e(Pn.prototype,{getNextCW:function(t){this.getEdges();var e=this.edgeList.indexOf(t),n=e-1;return 0===e&&(n=this.edgeList.size()-1),this.edgeList.get(n)},propagateSideLabels:function(t){for(var e=L.NONE,n=this.iterator();n.hasNext();){var i=n.next(),r=i.getLabel();r.isArea(t)&&r.getLocation(t,cn.LEFT)!==L.NONE&&(e=r.getLocation(t,cn.LEFT))}if(e===L.NONE)return null;for(var s=e,n=this.iterator();n.hasNext();){var i=n.next(),r=i.getLabel();if(r.getLocation(t,cn.ON)===L.NONE&&r.setLocation(t,cn.ON,s),r.isArea(t)){var o=r.getLocation(t,cn.LEFT),a=r.getLocation(t,cn.RIGHT);if(a!==L.NONE){if(a!==s)throw new sn("side location conflict",i.getCoordinate());o===L.NONE&&f.shouldNeverReachHere("found single null side (at "+i.getCoordinate()+")"),s=o}else f.isTrue(r.getLocation(t,cn.LEFT)===L.NONE,"found single null side"),r.setLocation(t,cn.RIGHT,s),r.setLocation(t,cn.LEFT,s)}}},getCoordinate:function(){var t=this.iterator();if(!t.hasNext())return null;var e=t.next();return e.getCoordinate()},print:function(t){A.out.println("EdgeEndStar:   "+this.getCoordinate());for(var e=this.iterator();e.hasNext();){var n=e.next();n.print(t)}},isAreaLabelsConsistent:function(t){return this.computeEdgeEndLabels(t.getBoundaryNodeRule()),this.checkAreaLabelsConsistent(0);
},checkAreaLabelsConsistent:function(t){var e=this.getEdges();if(e.size()<=0)return!0;var n=e.size()-1,i=e.get(n).getLabel(),r=i.getLocation(t,cn.LEFT);f.isTrue(r!==L.NONE,"Found unlabelled area edge");for(var s=r,o=this.iterator();o.hasNext();){var a=o.next(),u=a.getLabel();f.isTrue(u.isArea(t),"Found non-area edge");var l=u.getLocation(t,cn.LEFT),h=u.getLocation(t,cn.RIGHT);if(l===h)return!1;if(h!==s)return!1;s=l}return!0},findIndex:function(t){this.iterator();for(var e=0;e<this.edgeList.size();e++){var n=this.edgeList.get(e);if(n===t)return e}return-1},iterator:function(){return this.getEdges().iterator()},getEdges:function(){return null===this.edgeList&&(this.edgeList=new I(this.edgeMap.values())),this.edgeList},getLocation:function(t,e,n){return this.ptInAreaLocation[t]===L.NONE&&(this.ptInAreaLocation[t]=Tn.locate(e,n[t].getGeometry())),this.ptInAreaLocation[t]},toString:function(){var t=new P;t.append("EdgeEndStar:   "+this.getCoordinate()),t.append("\n");for(var e=this.iterator();e.hasNext();){var n=e.next();t.append(n),t.append("\n")}return t.toString()},computeEdgeEndLabels:function(t){for(var e=this.iterator();e.hasNext();){var n=e.next();n.computeLabel(t)}},computeLabelling:function(t){this.computeEdgeEndLabels(t[0].getBoundaryNodeRule()),this.propagateSideLabels(0),this.propagateSideLabels(1);for(var e=[!1,!1],n=this.iterator();n.hasNext();)for(var i=n.next(),r=i.getLabel(),s=0;2>s;s++)r.isLine(s)&&r.getLocation(s)===L.BOUNDARY&&(e[s]=!0);for(var n=this.iterator();n.hasNext();)for(var i=n.next(),r=i.getLabel(),s=0;2>s;s++)if(r.isAnyNull(s)){var o=L.NONE;if(e[s])o=L.EXTERIOR;else{var a=i.getCoordinate();o=this.getLocation(s,a,t)}r.setAllLocationsIfNull(s,o)}},getDegree:function(){return this.edgeMap.size()},insertEdgeEnd:function(t,e){this.edgeMap.put(t,e),this.edgeList=null},interfaces_:function(){return[]},getClass:function(){return Pn}}),h(bn,Pn),e(bn.prototype,{linkResultDirectedEdges:function(){this.getResultAreaEdges();for(var t=null,e=null,n=this.SCANNING_FOR_INCOMING,i=0;i<this.resultAreaEdgeList.size();i++){var r=this.resultAreaEdgeList.get(i),s=r.getSym();if(r.getLabel().isArea())switch(null===t&&r.isInResult()&&(t=r),n){case this.SCANNING_FOR_INCOMING:if(!s.isInResult())continue;e=s,n=this.LINKING_TO_OUTGOING;break;case this.LINKING_TO_OUTGOING:if(!r.isInResult())continue;e.setNext(r),n=this.SCANNING_FOR_INCOMING}}if(n===this.LINKING_TO_OUTGOING){if(null===t)throw new sn("no outgoing dirEdge found",this.getCoordinate());f.isTrue(t.isInResult(),"unable to link last incoming dirEdge"),e.setNext(t)}},insert:function(t){var e=t;this.insertEdgeEnd(e,e)},getRightmostEdge:function(){var t=this.getEdges(),e=t.size();if(1>e)return null;var n=t.get(0);if(1===e)return n;var i=t.get(e-1),r=n.getQuadrant(),s=i.getQuadrant();if(Je.isNorthern(r)&&Je.isNorthern(s))return n;if(!Je.isNorthern(r)&&!Je.isNorthern(s))return i;return 0!==n.getDy()?n:0!==i.getDy()?i:(f.shouldNeverReachHere("found two horizontal edges incident on node"),null)},print:function(t){A.out.println("DirectedEdgeStar: "+this.getCoordinate());for(var e=this.iterator();e.hasNext();){var n=e.next();t.print("out "),n.print(t),t.println(),t.print("in "),n.getSym().print(t),t.println()}},getResultAreaEdges:function(){if(null!==this.resultAreaEdgeList)return this.resultAreaEdgeList;this.resultAreaEdgeList=new I;for(var t=this.iterator();t.hasNext();){var e=t.next();(e.isInResult()||e.getSym().isInResult())&&this.resultAreaEdgeList.add(e)}return this.resultAreaEdgeList},updateLabelling:function(t){for(var e=this.iterator();e.hasNext();){var n=e.next(),i=n.getLabel();i.setAllLocationsIfNull(0,t.getLocation(0)),i.setAllLocationsIfNull(1,t.getLocation(1))}},linkAllDirectedEdges:function(){this.getEdges();for(var t=null,e=null,n=this.edgeList.size()-1;n>=0;n--){var i=this.edgeList.get(n),r=i.getSym();null===e&&(e=r),null!==t&&r.setNext(t),t=i}e.setNext(t)},computeDepths:function(){if(1===arguments.length){var t=arguments[0],e=this.findIndex(t),n=(t.getLabel(),t.getDepth(cn.LEFT)),i=t.getDepth(cn.RIGHT),r=this.computeDepths(e+1,this.edgeList.size(),n),s=this.computeDepths(0,e,r);if(s!==i)throw new sn("depth mismatch at "+t.getCoordinate())}else if(3===arguments.length){for(var o=arguments[0],a=arguments[1],u=arguments[2],l=u,h=o;a>h;h++){var c=this.edgeList.get(h);c.getLabel();c.setEdgeDepths(cn.RIGHT,l),l=c.getDepth(cn.LEFT)}return l}},mergeSymLabels:function(){for(var t=this.iterator();t.hasNext();){var e=t.next(),n=e.getLabel();n.merge(e.getSym().getLabel())}},linkMinimalDirectedEdges:function(t){for(var e=null,n=null,i=this.SCANNING_FOR_INCOMING,r=this.resultAreaEdgeList.size()-1;r>=0;r--){var s=this.resultAreaEdgeList.get(r),o=s.getSym();switch(null===e&&s.getEdgeRing()===t&&(e=s),i){case this.SCANNING_FOR_INCOMING:if(o.getEdgeRing()!==t)continue;n=o,i=this.LINKING_TO_OUTGOING;break;case this.LINKING_TO_OUTGOING:if(s.getEdgeRing()!==t)continue;n.setNextMin(s),i=this.SCANNING_FOR_INCOMING}}i===this.LINKING_TO_OUTGOING&&(f.isTrue(null!==e,"found null for first outgoing dirEdge"),f.isTrue(e.getEdgeRing()===t,"unable to link last incoming dirEdge"),n.setNextMin(e))},getOutgoingDegree:function(){if(0===arguments.length){for(var t=0,e=this.iterator();e.hasNext();){var n=e.next();n.isInResult()&&t++}return t}if(1===arguments.length){for(var i=arguments[0],t=0,e=this.iterator();e.hasNext();){var n=e.next();n.getEdgeRing()===i&&t++}return t}},getLabel:function(){return this.label},findCoveredLineEdges:function(){for(var t=L.NONE,e=this.iterator();e.hasNext();){var n=e.next(),i=n.getSym();if(!n.isLineEdge()){if(n.isInResult()){t=L.INTERIOR;break}if(i.isInResult()){t=L.EXTERIOR;break}}}if(t===L.NONE)return null;for(var r=t,e=this.iterator();e.hasNext();){var n=e.next(),i=n.getSym();n.isLineEdge()?n.getEdge().setCovered(r===L.INTERIOR):(n.isInResult()&&(r=L.EXTERIOR),i.isInResult()&&(r=L.INTERIOR))}},computeLabelling:function(t){Pn.prototype.computeLabelling.call(this,t),this.label=new gn(L.NONE);for(var e=this.iterator();e.hasNext();)for(var n=e.next(),i=n.getEdge(),r=i.getLabel(),s=0;2>s;s++){var o=r.getLocation(s);o!==L.INTERIOR&&o!==L.BOUNDARY||this.label.setLocation(s,L.INTERIOR)}},interfaces_:function(){return[]},getClass:function(){return bn}}),h(On,Nn),e(On.prototype,{createNode:function(t){return new yn(t,new bn)},interfaces_:function(){return[]},getClass:function(){return On}}),e(_n.prototype,{computeIntersections:function(t,e){this.mce.computeIntersectsForChain(this.chainIndex,t.mce,t.chainIndex,e)},interfaces_:function(){return[]},getClass:function(){return _n}}),e(Mn.prototype,{isDelete:function(){return this.eventType===Mn.DELETE},setDeleteEventIndex:function(t){this.deleteEventIndex=t},getObject:function(){return this.obj},compareTo:function(t){var e=t;return this.xValue<e.xValue?-1:this.xValue>e.xValue?1:this.eventType<e.eventType?-1:this.eventType>e.eventType?1:0},getInsertEvent:function(){return this.insertEvent},isInsert:function(){return this.eventType===Mn.INSERT},isSameLabel:function(t){return null===this.label?!1:this.label===t.label},getDeleteEventIndex:function(){return this.deleteEventIndex},interfaces_:function(){return[s]},getClass:function(){return Mn}}),Mn.INSERT=1,Mn.DELETE=2,e(Dn.prototype,{interfaces_:function(){return[]},getClass:function(){return Dn}}),e(An.prototype,{isTrivialIntersection:function(t,e,n,i){if(t===n&&1===this.li.getIntersectionNum()){if(An.isAdjacentSegments(e,i))return!0;if(t.isClosed()){var r=t.getNumPoints()-1;if(0===e&&i===r||0===i&&e===r)return!0}}return!1},getProperIntersectionPoint:function(){return this.properIntersectionPoint},setIsDoneIfProperInt:function(t){this.isDoneWhenProperInt=t},hasProperInteriorIntersection:function(){return this.hasProperInterior},isBoundaryPointInternal:function(t,e){for(var n=e.iterator();n.hasNext();){var i=n.next(),r=i.getCoordinate();if(t.isIntersection(r))return!0}return!1},hasProperIntersection:function(){return this.hasProper},hasIntersection:function(){return this._hasIntersection},isDone:function(){return this._isDone},isBoundaryPoint:function(t,e){return null===e?!1:this.isBoundaryPointInternal(t,e[0])?!0:!!this.isBoundaryPointInternal(t,e[1])},setBoundaryNodes:function(t,e){this.bdyNodes=new Array(2).fill(null),this.bdyNodes[0]=t,this.bdyNodes[1]=e},addIntersections:function(t,e,n,i){if(t===n&&e===i)return null;this.numTests++;var r=t.getCoordinates()[e],s=t.getCoordinates()[e+1],o=n.getCoordinates()[i],a=n.getCoordinates()[i+1];this.li.computeIntersection(r,s,o,a),this.li.hasIntersection()&&(this.recordIsolated&&(t.setIsolated(!1),n.setIsolated(!1)),this.numIntersections++,this.isTrivialIntersection(t,e,n,i)||(this._hasIntersection=!0,!this.includeProper&&this.li.isProper()||(t.addIntersections(this.li,e,0),n.addIntersections(this.li,i,1)),this.li.isProper()&&(this.properIntersectionPoint=this.li.getIntersection(0).copy(),this.hasProper=!0,this.isDoneWhenProperInt&&(this._isDone=!0),this.isBoundaryPoint(this.li,this.bdyNodes)||(this.hasProperInterior=!0))))},interfaces_:function(){return[]},getClass:function(){return An}}),An.isAdjacentSegments=function(t,e){return 1===Math.abs(t-e)},h(Fn,Dn),e(Fn.prototype,{prepareEvents:function(){ho.sort(this.events);for(var t=0;t<this.events.size();t++){var e=this.events.get(t);e.isDelete()&&e.getInsertEvent().setDeleteEventIndex(t)}},computeIntersections:function(){if(1===arguments.length){var t=arguments[0];this.nOverlaps=0,this.prepareEvents();for(var e=0;e<this.events.size();e++){var n=this.events.get(e);if(n.isInsert()&&this.processOverlaps(e,n.getDeleteEventIndex(),n,t),t.isDone())break}}else if(3===arguments.length)if(arguments[2]instanceof An&&R(arguments[0],y)&&R(arguments[1],y)){var i=arguments[0],r=arguments[1],s=arguments[2];this.addEdges(i,i),this.addEdges(r,r),this.computeIntersections(s)}else if("boolean"==typeof arguments[2]&&R(arguments[0],y)&&arguments[1]instanceof An){var o=arguments[0],a=arguments[1],u=arguments[2];u?this.addEdges(o,null):this.addEdges(o),this.computeIntersections(a)}},addEdge:function(t,e){for(var n=t.getMonotoneChainEdge(),i=n.getStartIndexes(),r=0;r<i.length-1;r++){var s=new _n(n,r),o=new Mn(e,n.getMinX(r),s);this.events.add(o),this.events.add(new Mn(n.getMaxX(r),o))}},processOverlaps:function(t,e,n,i){for(var r=n.getObject(),s=t;e>s;s++){var o=this.events.get(s);if(o.isInsert()){var a=o.getObject();n.isSameLabel(o)||(r.computeIntersections(a,i),this.nOverlaps++)}}},addEdges:function(){if(1===arguments.length)for(var t=arguments[0],e=t.iterator();e.hasNext();){var n=e.next();this.addEdge(n,n)}else if(2===arguments.length)for(var i=arguments[0],r=arguments[1],e=i.iterator();e.hasNext();){var n=e.next();this.addEdge(n,r)}},interfaces_:function(){return[]},getClass:function(){return Fn}}),e(Gn.prototype,{getMin:function(){return this.min},intersects:function(t,e){return!(this.min>e||this.max<t)},getMax:function(){return this.max},toString:function(){return se.toLineString(new g(this.min,0),new g(this.max,0))},interfaces_:function(){return[]},getClass:function(){return Gn}}),e(qn.prototype,{compare:function(t,e){var n=t,i=e,r=(n.min+n.max)/2,s=(i.min+i.max)/2;return s>r?-1:r>s?1:0},interfaces_:function(){return[a]},getClass:function(){return qn}}),Gn.NodeComparator=qn,h(Bn,Gn),e(Bn.prototype,{query:function(t,e,n){return this.intersects(t,e)?void n.visitItem(this.item):null},interfaces_:function(){return[]},getClass:function(){return Bn}}),h(zn,Gn),e(zn.prototype,{buildExtent:function(t,e){this.min=Math.min(t.min,e.min),this.max=Math.max(t.max,e.max)},query:function(t,e,n){return this.intersects(t,e)?(null!==this.node1&&this.node1.query(t,e,n),void(null!==this.node2&&this.node2.query(t,e,n))):null},interfaces_:function(){return[]},getClass:function(){return zn}}),e(Vn.prototype,{buildTree:function(){ho.sort(this.leaves,new IntervalRTreeNode.NodeComparator);for(var t=this.leaves,e=null,n=new I;;){if(this.buildLevel(t,n),1===n.size())return n.get(0);e=t,t=n,n=e}},insert:function(t,e,n){if(null!==this.root)throw new IllegalStateException("Index cannot be added to once it has been queried");this.leaves.add(new Bn(t,e,n))},query:function(t,e,n){this.init(),this.root.query(t,e,n)},buildRoot:function(){return null!==this.root?null:void(this.root=this.buildTree())},printNode:function(t){A.out.println(se.toLineString(new g(t.min,this.level),new g(t.max,this.level)))},init:function(){return null!==this.root?null:void this.buildRoot()},buildLevel:function(t,e){this.level++,e.clear();for(var n=0;n<t.size();n+=2){var i=t.get(n),r=n+1<t.size()?t.get(n):null;if(null===r)e.add(i);else{var s=new zn(t.get(n),t.get(n+1));e.add(s)}}},interfaces_:function(){return[]},getClass:function(){return Vn}}),e(kn.prototype,{filter:function(t){if(this.isForcedToLineString&&t instanceof bt){var e=t.getFactory().createLineString(t.getCoordinateSequence());return this.lines.add(e),null}t instanceof St&&this.lines.add(t)},setForceToLineString:function(t){this.isForcedToLineString=t},interfaces_:function(){return[q]},getClass:function(){return kn}}),kn.getGeometry=function(){if(1===arguments.length){var t=arguments[0];return t.getFactory().buildGeometry(kn.getLines(t))}if(2===arguments.length){var e=arguments[0],n=arguments[1];return e.getFactory().buildGeometry(kn.getLines(e,n))}},kn.getLines=function(){if(1===arguments.length){var t=arguments[0];return kn.getLines(t,!1)}if(2===arguments.length){if(R(arguments[0],v)&&R(arguments[1],v)){for(var e=arguments[0],n=arguments[1],i=e.iterator();i.hasNext();){var r=i.next();kn.getLines(r,n)}return n}if(arguments[0]instanceof B&&"boolean"==typeof arguments[1]){var s=arguments[0],o=arguments[1],a=new I;return s.apply(new kn(a,o)),a}if(arguments[0]instanceof B&&R(arguments[1],v)){var u=arguments[0],l=arguments[1];return u instanceof St?l.add(u):u.apply(new kn(l)),l}}else if(3===arguments.length){if("boolean"==typeof arguments[2]&&R(arguments[0],v)&&R(arguments[1],v)){for(var h=arguments[0],c=arguments[1],f=arguments[2],i=h.iterator();i.hasNext();){var r=i.next();kn.getLines(r,c,f)}return c}if("boolean"==typeof arguments[2]&&arguments[0]instanceof B&&R(arguments[1],v)){var g=arguments[0],d=arguments[1],p=arguments[2];return g.apply(new kn(d,p)),d}}},e(Yn.prototype,{visitItem:function(t){this.items.add(t)},getItems:function(){return this.items},interfaces_:function(){return[Ae]},getClass:function(){return Yn}}),e(Un.prototype,{locate:function(t){var e=new le(t),n=new Xn(e);return this.index.query(t.y,t.y,n),e.getLocation()},interfaces_:function(){return[Rn]},getClass:function(){return Un}}),e(Xn.prototype,{visitItem:function(t){var e=t;this.counter.countSegment(e.getCoordinate(0),e.getCoordinate(1))},interfaces_:function(){return[Ae]},getClass:function(){return Xn}}),e(Hn.prototype,{init:function(t){for(var e=kn.getLines(t),n=e.iterator();n.hasNext();){var i=n.next(),r=i.getCoordinates();this.addLine(r)}},addLine:function(t){for(var e=1;e<t.length;e++){var n=new ce(t[e-1],t[e]),i=Math.min(n.p0.y,n.p1.y),r=Math.max(n.p0.y,n.p1.y);this.index.insert(i,r,n)}},query:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=new Yn;return this.index.query(t,e,n),n.getItems()}if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];this.index.query(i,r,s)}},interfaces_:function(){return[]},getClass:function(){return Hn}}),Un.SegmentVisitor=Xn,Un.IntervalIndexedGeometry=Hn,e(Wn.prototype,{getSegmentIndex:function(){return this.segmentIndex},getCoordinate:function(){return this.coord},print:function(t){t.print(this.coord),t.print(" seg # = "+this.segmentIndex),t.println(" dist = "+this.dist)},compareTo:function(t){var e=t;return this.compare(e.segmentIndex,e.dist)},isEndPoint:function(t){return 0===this.segmentIndex&&0===this.dist?!0:this.segmentIndex===t},toString:function(){return this.coord+" seg # = "+this.segmentIndex+" dist = "+this.dist},getDistance:function(){return this.dist},compare:function(t,e){return this.segmentIndex<t?-1:this.segmentIndex>t?1:this.dist<e?-1:this.dist>e?1:0},interfaces_:function(){return[s]},getClass:function(){return Wn}}),e(jn.prototype,{print:function(t){t.println("Intersections:");for(var e=this.iterator();e.hasNext();){var n=e.next();n.print(t)}},iterator:function(){return this.nodeMap.values().iterator()},addSplitEdges:function(t){this.addEndpoints();for(var e=this.iterator(),n=e.next();e.hasNext();){var i=e.next(),r=this.createSplitEdge(n,i);t.add(r),n=i}},addEndpoints:function(){var t=this.edge.pts.length-1;this.add(this.edge.pts[0],0,0),this.add(this.edge.pts[t],t,0)},createSplitEdge:function(t,e){var n=e.segmentIndex-t.segmentIndex+2,i=this.edge.pts[e.segmentIndex],r=e.dist>0||!e.coord.equals2D(i);r||n--;var s=new Array(n).fill(null),o=0;s[o++]=new g(t.coord);for(var a=t.segmentIndex+1;a<=e.segmentIndex;a++)s[o++]=this.edge.pts[a];return r&&(s[o]=e.coord),new Jn(s,new gn(this.edge.label))},add:function(t,e,n){var i=new Wn(t,e,n),r=this.nodeMap.get(i);return null!==r?r:(this.nodeMap.put(i,i),i)},isIntersection:function(t){for(var e=this.iterator();e.hasNext();){var n=e.next();if(n.coord.equals(t))return!0}return!1},interfaces_:function(){return[]},getClass:function(){return jn}}),e(Kn.prototype,{getChainStartIndices:function(t){var e=0,n=new I;n.add(new b(e));do{var i=this.findChainEnd(t,e);n.add(new b(i)),e=i}while(e<t.length-1);var r=Kn.toIntArray(n);return r},findChainEnd:function(t,e){for(var n=Je.quadrant(t[e],t[e+1]),i=e+1;i<t.length;){var r=Je.quadrant(t[i-1],t[i]);if(r!==n)break;i++}return i-1},interfaces_:function(){return[]},getClass:function(){return Kn}}),Kn.toIntArray=function(t){for(var e=new Array(t.size()).fill(null),n=0;n<e.length;n++)e[n]=t.get(n).intValue();return e},e(Zn.prototype,{getCoordinates:function(){return this.pts},getMaxX:function(t){var e=this.pts[this.startIndex[t]].x,n=this.pts[this.startIndex[t+1]].x;return e>n?e:n},getMinX:function(t){var e=this.pts[this.startIndex[t]].x,n=this.pts[this.startIndex[t+1]].x;return n>e?e:n},computeIntersectsForChain:function(){if(4===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],i=arguments[3];this.computeIntersectsForChain(this.startIndex[t],this.startIndex[t+1],e,e.startIndex[n],e.startIndex[n+1],i)}else if(6===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],a=arguments[3],u=arguments[4],l=arguments[5],h=this.pts[r],c=this.pts[s],f=o.pts[a],g=o.pts[u];if(s-r===1&&u-a===1)return l.addIntersections(this.e,r,o.e,a),null;if(this.env1.init(h,c),this.env2.init(f,g),!this.env1.intersects(this.env2))return null;var d=Math.trunc((r+s)/2),p=Math.trunc((a+u)/2);d>r&&(p>a&&this.computeIntersectsForChain(r,d,o,a,p,l),u>p&&this.computeIntersectsForChain(r,d,o,p,u,l)),s>d&&(p>a&&this.computeIntersectsForChain(d,s,o,a,p,l),u>p&&this.computeIntersectsForChain(d,s,o,p,u,l))}},getStartIndexes:function(){return this.startIndex},computeIntersects:function(t,e){for(var n=0;n<this.startIndex.length-1;n++)for(var i=0;i<t.startIndex.length-1;i++)this.computeIntersectsForChain(n,t,i,e)},interfaces_:function(){return[]},getClass:function(){return Zn}}),e(Qn.prototype,{getDepth:function(t,e){return this.depth[t][e]},setDepth:function(t,e,n){this.depth[t][e]=n},isNull:function(){if(0===arguments.length){for(var t=0;2>t;t++)for(var e=0;3>e;e++)if(this.depth[t][e]!==Qn.NULL_VALUE)return!1;return!0}if(1===arguments.length){var n=arguments[0];return this.depth[n][1]===Qn.NULL_VALUE}if(2===arguments.length){var i=arguments[0],r=arguments[1];return this.depth[i][r]===Qn.NULL_VALUE}},normalize:function(){for(var t=0;2>t;t++)if(!this.isNull(t)){var e=this.depth[t][1];this.depth[t][2]<e&&(e=this.depth[t][2]),0>e&&(e=0);for(var n=1;3>n;n++){var i=0;this.depth[t][n]>e&&(i=1),this.depth[t][n]=i}}},getDelta:function(t){return this.depth[t][cn.RIGHT]-this.depth[t][cn.LEFT]},getLocation:function(t,e){return this.depth[t][e]<=0?L.EXTERIOR:L.INTERIOR},toString:function(){return"A: "+this.depth[0][1]+","+this.depth[0][2]+" B: "+this.depth[1][1]+","+this.depth[1][2]},add:function(){if(1===arguments.length)for(var t=arguments[0],e=0;2>e;e++)for(var n=1;3>n;n++){var i=t.getLocation(e,n);i!==L.EXTERIOR&&i!==L.INTERIOR||(this.isNull(e,n)?this.depth[e][n]=Qn.depthAtLocation(i):this.depth[e][n]+=Qn.depthAtLocation(i))}else if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];o===L.INTERIOR&&this.depth[r][s]++}},interfaces_:function(){return[]},getClass:function(){return Qn}}),Qn.depthAtLocation=function(t){return t===L.EXTERIOR?0:t===L.INTERIOR?1:Qn.NULL_VALUE},Qn.NULL_VALUE=-1,h(Jn,mn),e(Jn.prototype,{getDepth:function(){return this.depth},getCollapsedEdge:function(){var t=new Array(2).fill(null);t[0]=this.pts[0],t[1]=this.pts[1];var e=new Jn(t,gn.toLineLabel(this.label));return e},isIsolated:function(){return this._isIsolated},getCoordinates:function(){return this.pts},setIsolated:function(t){this._isIsolated=t},setName:function(t){this.name=t},equals:function(t){if(!(t instanceof Jn))return!1;var e=t;if(this.pts.length!==e.pts.length)return!1;for(var n=!0,i=!0,r=this.pts.length,s=0;s<this.pts.length;s++)if(this.pts[s].equals2D(e.pts[s])||(n=!1),this.pts[s].equals2D(e.pts[--r])||(i=!1),!n&&!i)return!1;return!0},getCoordinate:function(){if(0===arguments.length)return this.pts.length>0?this.pts[0]:null;if(1===arguments.length){var t=arguments[0];return this.pts[t]}},print:function(t){t.print("edge "+this.name+": "),t.print("LINESTRING (");for(var e=0;e<this.pts.length;e++)e>0&&t.print(","),t.print(this.pts[e].x+" "+this.pts[e].y);t.print(")  "+this.label+" "+this.depthDelta)},computeIM:function(t){Jn.updateIM(this.label,t)},isCollapsed:function(){return this.label.isArea()?3!==this.pts.length?!1:!!this.pts[0].equals(this.pts[2]):!1},isClosed:function(){return this.pts[0].equals(this.pts[this.pts.length-1])},getMaximumSegmentIndex:function(){return this.pts.length-1},getDepthDelta:function(){return this.depthDelta},getNumPoints:function(){return this.pts.length},printReverse:function(t){t.print("edge "+this.name+": ");for(var e=this.pts.length-1;e>=0;e--)t.print(this.pts[e]+" ");t.println("")},getMonotoneChainEdge:function(){return null===this.mce&&(this.mce=new Zn(this)),this.mce},getEnvelope:function(){if(null===this.env){this.env=new C;for(var t=0;t<this.pts.length;t++)this.env.expandToInclude(this.pts[t])}return this.env},addIntersection:function(t,e,n,i){var r=new g(t.getIntersection(i)),s=e,o=t.getEdgeDistance(n,i),a=s+1;if(a<this.pts.length){var u=this.pts[a];r.equals2D(u)&&(s=a,o=0)}this.eiList.add(r,s,o)},toString:function(){var t=new P;t.append("edge "+this.name+": "),t.append("LINESTRING (");for(var e=0;e<this.pts.length;e++)e>0&&t.append(","),t.append(this.pts[e].x+" "+this.pts[e].y);return t.append(")  "+this.label+" "+this.depthDelta),t.toString()},isPointwiseEqual:function(t){if(this.pts.length!==t.pts.length)return!1;for(var e=0;e<this.pts.length;e++)if(!this.pts[e].equals2D(t.pts[e]))return!1;return!0},setDepthDelta:function(t){this.depthDelta=t},getEdgeIntersectionList:function(){return this.eiList},addIntersections:function(t,e,n){for(var i=0;i<t.getIntersectionNum();i++)this.addIntersection(t,e,n,i)},interfaces_:function(){return[]},getClass:function(){return Jn}}),Jn.updateIM=function(){if(2!==arguments.length)return mn.prototype.updateIM.apply(this,arguments);var t=arguments[0],e=arguments[1];e.setAtLeastIfValid(t.getLocation(0,cn.ON),t.getLocation(1,cn.ON),1),t.isArea()&&(e.setAtLeastIfValid(t.getLocation(0,cn.LEFT),t.getLocation(1,cn.LEFT),2),e.setAtLeastIfValid(t.getLocation(0,cn.RIGHT),t.getLocation(1,cn.RIGHT),2))},h($n,Cn),e($n.prototype,{insertBoundaryPoint:function(t,e){var n=this.nodes.addNode(e),i=n.getLabel(),r=1,s=L.NONE;s=i.getLocation(t,cn.ON),s===L.BOUNDARY&&r++;var o=$n.determineBoundary(this.boundaryNodeRule,r);i.setLocation(t,o)},computeSelfNodes:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.computeSelfNodes(t,e,!1)}if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2],s=new An(n,!0,!1);s.setIsDoneIfProperInt(r);var o=this.createEdgeSetIntersector(),a=this.parentGeom instanceof bt||this.parentGeom instanceof Tt||this.parentGeom instanceof Ot,u=i||!a;return o.computeIntersections(this.edges,s,u),this.addSelfIntersectionNodes(this.argIndex),s}},computeSplitEdges:function(t){for(var e=this.edges.iterator();e.hasNext();){var n=e.next();n.eiList.addSplitEdges(t)}},computeEdgeIntersections:function(t,e,n){var i=new An(e,n,!0);i.setBoundaryNodes(this.getBoundaryNodes(),t.getBoundaryNodes());var r=this.createEdgeSetIntersector();return r.computeIntersections(this.edges,t.edges,i),i},getGeometry:function(){return this.parentGeom},getBoundaryNodeRule:function(){return this.boundaryNodeRule},hasTooFewPoints:function(){return this._hasTooFewPoints},addPoint:function(){if(arguments[0]instanceof Lt){var t=arguments[0],e=t.getCoordinate();this.insertPoint(this.argIndex,e,L.INTERIOR)}else if(arguments[0]instanceof g){var n=arguments[0];this.insertPoint(this.argIndex,n,L.INTERIOR)}},addPolygon:function(t){this.addPolygonRing(t.getExteriorRing(),L.EXTERIOR,L.INTERIOR);for(var e=0;e<t.getNumInteriorRing();e++){var n=t.getInteriorRingN(e);this.addPolygonRing(n,L.INTERIOR,L.EXTERIOR)}},addEdge:function(t){this.insertEdge(t);var e=t.getCoordinates();this.insertPoint(this.argIndex,e[0],L.BOUNDARY),this.insertPoint(this.argIndex,e[e.length-1],L.BOUNDARY)},addLineString:function(t){var e=H.removeRepeatedPoints(t.getCoordinates());if(e.length<2)return this._hasTooFewPoints=!0,this.invalidPoint=e[0],null;var n=new Jn(e,new gn(this.argIndex,L.INTERIOR));this.lineEdgeMap.put(t,n),this.insertEdge(n),f.isTrue(e.length>=2,"found LineString with single point"),this.insertBoundaryPoint(this.argIndex,e[0]),this.insertBoundaryPoint(this.argIndex,e[e.length-1])},getInvalidPoint:function(){return this.invalidPoint},getBoundaryPoints:function(){for(var t=this.getBoundaryNodes(),e=new Array(t.size()).fill(null),n=0,i=t.iterator();i.hasNext();){var r=i.next();e[n++]=r.getCoordinate().copy()}return e},getBoundaryNodes:function(){return null===this.boundaryNodes&&(this.boundaryNodes=this.nodes.getBoundaryNodes(this.argIndex)),this.boundaryNodes},addSelfIntersectionNode:function(t,e,n){return this.isBoundaryNode(t,e)?null:void(n===L.BOUNDARY&&this.useBoundaryDeterminationRule?this.insertBoundaryPoint(t,e):this.insertPoint(t,e,n))},addPolygonRing:function(t,e,n){if(t.isEmpty())return null;var i=H.removeRepeatedPoints(t.getCoordinates());if(i.length<4)return this._hasTooFewPoints=!0,this.invalidPoint=i[0],null;var r=e,s=n;he.isCCW(i)&&(r=n,s=e);var o=new Jn(i,new gn(this.argIndex,L.BOUNDARY,r,s));this.lineEdgeMap.put(t,o),this.insertEdge(o),this.insertPoint(this.argIndex,i[0],L.BOUNDARY)},insertPoint:function(t,e,n){var i=this.nodes.addNode(e),r=i.getLabel();null===r?i.label=new gn(t,n):r.setLocation(t,n)},createEdgeSetIntersector:function(){return new Fn},addSelfIntersectionNodes:function(t){for(var e=this.edges.iterator();e.hasNext();)for(var n=e.next(),i=n.getLabel().getLocation(t),r=n.eiList.iterator();r.hasNext();){var s=r.next();this.addSelfIntersectionNode(t,s.coord,i)}},add:function(){if(1!==arguments.length)return Cn.prototype.add.apply(this,arguments);var t=arguments[0];if(t.isEmpty())return null;if(t instanceof Ot&&(this.useBoundaryDeterminationRule=!1),t instanceof Tt)this.addPolygon(t);else if(t instanceof St)this.addLineString(t);else if(t instanceof Lt)this.addPoint(t);else if(t instanceof Pt)this.addCollection(t);else if(t instanceof gt)this.addCollection(t);else if(t instanceof Ot)this.addCollection(t);else{if(!(t instanceof ft))throw new UnsupportedOperationException(t.getClass().getName());this.addCollection(t)}},addCollection:function(t){for(var e=0;e<t.getNumGeometries();e++){var n=t.getGeometryN(e);this.add(n)}},locate:function(t){return R(this.parentGeom,Rt)&&this.parentGeom.getNumGeometries()>50?(null===this.areaPtLocator&&(this.areaPtLocator=new Un(this.parentGeom)),this.areaPtLocator.locate(t)):this.ptLocator.locate(t,this.parentGeom)},findEdge:function(){if(1===arguments.length){var t=arguments[0];return this.lineEdgeMap.get(t)}return Cn.prototype.findEdge.apply(this,arguments)},interfaces_:function(){return[]},getClass:function(){return $n}}),$n.determineBoundary=function(t,e){return t.isInBoundary(e)?L.BOUNDARY:L.INTERIOR},e(ti.prototype,{getArgGeometry:function(t){return this.arg[t].getGeometry()},setComputationPrecision:function(t){this.resultPrecisionModel=t,this.li.setPrecisionModel(this.resultPrecisionModel)},interfaces_:function(){return[]},getClass:function(){return ti}}),e(ei.prototype,{compareTo:function(t){var e=t,n=ei.compareOriented(this.pts,this._orientation,e.pts,e._orientation);return n},interfaces_:function(){return[s]},getClass:function(){return ei}}),ei.orientation=function(t){return 1===H.increasingDirection(t)},ei.compareOriented=function(t,e,n,i){for(var r=e?1:-1,s=i?1:-1,o=e?t.length:-1,a=i?n.length:-1,u=e?0:t.length-1,l=i?0:n.length-1;;){var h=t[u].compareTo(n[l]);if(0!==h)return h;u+=r,l+=s;var c=u===o,f=l===a;if(c&&!f)return-1;if(!c&&f)return 1;if(c&&f)return 0}},e(ni.prototype,{print:function(t){t.print("MULTILINESTRING ( ");for(var e=0;e<this.edges.size();e++){var n=this.edges.get(e);e>0&&t.print(","),t.print("(");for(var i=n.getCoordinates(),r=0;r<i.length;r++)r>0&&t.print(","),t.print(i[r].x+" "+i[r].y);t.println(")")}t.print(")  ")},addAll:function(t){for(var e=t.iterator();e.hasNext();)this.add(e.next())},findEdgeIndex:function(t){for(var e=0;e<this.edges.size();e++)if(this.edges.get(e).equals(t))return e;return-1},iterator:function(){return this.edges.iterator()},getEdges:function(){return this.edges},get:function(t){return this.edges.get(t)},findEqualEdge:function(t){var e=new ei(t.getCoordinates()),n=this.ocaMap.get(e);return n},add:function(t){this.edges.add(t);var e=new ei(t.getCoordinates());this.ocaMap.put(e,t)},interfaces_:function(){return[]},getClass:function(){return ni}}),h(ii,ti),e(ii.prototype,{insertUniqueEdge:function(t){var e=this.edgeList.findEqualEdge(t);if(null!==e){var n=e.getLabel(),i=t.getLabel();e.isPointwiseEqual(t)||(i=new gn(t.getLabel()),i.flip());var r=e.getDepth();r.isNull()&&r.add(n),r.add(i),n.merge(i)}else this.edgeList.add(t)},getGraph:function(){return this.graph},cancelDuplicateResultEdges:function(){for(var t=this.graph.getEdgeEnds().iterator();t.hasNext();){var e=t.next(),n=e.getSym();e.isInResult()&&n.isInResult()&&(e.setInResult(!1),n.setInResult(!1))}},isCoveredByLA:function(t){return this.isCovered(t,this.resultLineList)?!0:!!this.isCovered(t,this.resultPolyList)},computeGeometry:function(t,e,n,i){var r=new I;return r.addAll(t),r.addAll(e),r.addAll(n),r.isEmpty()?ii.createEmptyResult(i,this.arg[0].getGeometry(),this.arg[1].getGeometry(),this.geomFact):this.geomFact.buildGeometry(r)},mergeSymLabels:function(){for(var t=this.graph.getNodes().iterator();t.hasNext();){var e=t.next();e.getEdges().mergeSymLabels()}},isCovered:function(t,e){for(var n=e.iterator();n.hasNext();){var i=n.next(),r=this.ptLocator.locate(t,i);if(r!==L.EXTERIOR)return!0}return!1},replaceCollapsedEdges:function(){for(var t=new I,e=this.edgeList.iterator();e.hasNext();){var n=e.next();n.isCollapsed()&&(e.remove(),t.add(n.getCollapsedEdge()))}this.edgeList.addAll(t)},updateNodeLabelling:function(){for(var t=this.graph.getNodes().iterator();t.hasNext();){var e=t.next(),n=e.getEdges().getLabel();e.getLabel().merge(n)}},getResultGeometry:function(t){return this.computeOverlay(t),this.resultGeom},insertUniqueEdges:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.insertUniqueEdge(n)}},computeOverlay:function(t){this.copyPoints(0),this.copyPoints(1),this.arg[0].computeSelfNodes(this.li,!1),this.arg[1].computeSelfNodes(this.li,!1),this.arg[0].computeEdgeIntersections(this.arg[1],this.li,!0);var e=new I;this.arg[0].computeSplitEdges(e),this.arg[1].computeSplitEdges(e);this.insertUniqueEdges(e),this.computeLabelsFromDepths(),this.replaceCollapsedEdges(),ln.checkValid(this.edgeList.getEdges()),this.graph.addEdges(this.edgeList.getEdges()),this.computeLabelling(),this.labelIncompleteNodes(),this.findResultAreaEdges(t),
this.cancelDuplicateResultEdges();var n=new Sn(this.geomFact);n.add(this.graph),this.resultPolyList=n.getPolygons();var i=new wn(this,this.geomFact,this.ptLocator);this.resultLineList=i.build(t);var r=new Ln(this,this.geomFact,this.ptLocator);this.resultPointList=r.build(t),this.resultGeom=this.computeGeometry(this.resultPointList,this.resultLineList,this.resultPolyList,t)},labelIncompleteNode:function(t,e){var n=this.ptLocator.locate(t.getCoordinate(),this.arg[e].getGeometry());t.getLabel().setLocation(e,n)},copyPoints:function(t){for(var e=this.arg[t].getNodeIterator();e.hasNext();){var n=e.next(),i=this.graph.addNode(n.getCoordinate());i.setLabel(t,n.getLabel().getLocation(t))}},findResultAreaEdges:function(t){for(var e=this.graph.getEdgeEnds().iterator();e.hasNext();){var n=e.next(),i=n.getLabel();i.isArea()&&!n.isInteriorAreaEdge()&&ii.isResultOfOp(i.getLocation(0,cn.RIGHT),i.getLocation(1,cn.RIGHT),t)&&n.setInResult(!0)}},computeLabelsFromDepths:function(){for(var t=this.edgeList.iterator();t.hasNext();){var e=t.next(),n=e.getLabel(),i=e.getDepth();if(!i.isNull()){i.normalize();for(var r=0;2>r;r++)n.isNull(r)||!n.isArea()||i.isNull(r)||(0===i.getDelta(r)?n.toLine(r):(f.isTrue(!i.isNull(r,cn.LEFT),"depth of LEFT side has not been initialized"),n.setLocation(r,cn.LEFT,i.getLocation(r,cn.LEFT)),f.isTrue(!i.isNull(r,cn.RIGHT),"depth of RIGHT side has not been initialized"),n.setLocation(r,cn.RIGHT,i.getLocation(r,cn.RIGHT))))}}},computeLabelling:function(){for(var t=this.graph.getNodes().iterator();t.hasNext();){var e=t.next();e.getEdges().computeLabelling(this.arg)}this.mergeSymLabels(),this.updateNodeLabelling()},labelIncompleteNodes:function(){for(var t=0,e=this.graph.getNodes().iterator();e.hasNext();){var n=e.next(),i=n.getLabel();n.isIsolated()&&(t++,i.isNull(0)?this.labelIncompleteNode(n,0):this.labelIncompleteNode(n,1)),n.getEdges().updateLabelling(i)}},isCoveredByA:function(t){return!!this.isCovered(t,this.resultPolyList)},interfaces_:function(){return[]},getClass:function(){return ii}}),ii.overlayOp=function(t,e,n){var i=new ii(t,e),r=i.getResultGeometry(n);return r},ii.intersection=function(t,e){if(t.isEmpty()||e.isEmpty())return ii.createEmptyResult(ii.INTERSECTION,t,e,t.getFactory());if(t.isGeometryCollection()){var n=e;return hn.map(t,{interfaces_:function(){return[MapOp]},map:function(t){return t.intersection(n)}})}return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),si.overlayOp(t,e,ii.INTERSECTION)},ii.symDifference=function(t,e){if(t.isEmpty()||e.isEmpty()){if(t.isEmpty()&&e.isEmpty())return ii.createEmptyResult(ii.SYMDIFFERENCE,t,e,t.getFactory());if(t.isEmpty())return e.copy();if(e.isEmpty())return t.copy()}return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),si.overlayOp(t,e,ii.SYMDIFFERENCE)},ii.resultDimension=function(t,e,n){var i=e.getDimension(),r=n.getDimension(),s=-1;switch(t){case ii.INTERSECTION:s=Math.min(i,r);break;case ii.UNION:s=Math.max(i,r);break;case ii.DIFFERENCE:s=i;break;case ii.SYMDIFFERENCE:s=Math.max(i,r)}return s},ii.createEmptyResult=function(t,e,n,i){var r=null;switch(ii.resultDimension(t,e,n)){case-1:r=i.createGeometryCollection(new Array(0).fill(null));break;case 0:r=i.createPoint();break;case 1:r=i.createLineString();break;case 2:r=i.createPolygon()}return r},ii.difference=function(t,e){return t.isEmpty()?ii.createEmptyResult(ii.DIFFERENCE,t,e,t.getFactory()):e.isEmpty()?t.copy():(t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),si.overlayOp(t,e,ii.DIFFERENCE))},ii.isResultOfOp=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=t.getLocation(0),i=t.getLocation(1);return ii.isResultOfOp(n,i,e)}if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2];switch(r===L.BOUNDARY&&(r=L.INTERIOR),s===L.BOUNDARY&&(s=L.INTERIOR),o){case ii.INTERSECTION:return r===L.INTERIOR&&s===L.INTERIOR;case ii.UNION:return r===L.INTERIOR||s===L.INTERIOR;case ii.DIFFERENCE:return r===L.INTERIOR&&s!==L.INTERIOR;case ii.SYMDIFFERENCE:return r===L.INTERIOR&&s!==L.INTERIOR||r!==L.INTERIOR&&s===L.INTERIOR}return!1}},ii.INTERSECTION=1,ii.UNION=2,ii.DIFFERENCE=3,ii.SYMDIFFERENCE=4,e(ri.prototype,{selfSnap:function(t){var e=new Ie(t),n=e.snapTo(t,this.snapTolerance);return n},removeCommonBits:function(t){this.cbr=new Se,this.cbr.add(t[0]),this.cbr.add(t[1]);var e=new Array(2).fill(null);return e[0]=this.cbr.removeCommonBits(t[0].copy()),e[1]=this.cbr.removeCommonBits(t[1].copy()),e},prepareResult:function(t){return this.cbr.addCommonBits(t),t},getResultGeometry:function(t){var e=this.snap(this.geom),n=ii.overlayOp(e[0],e[1],t);return this.prepareResult(n)},checkValid:function(t){t.isValid()||A.out.println("Snapped geometry is invalid")},computeSnapTolerance:function(){this.snapTolerance=Ie.computeOverlaySnapTolerance(this.geom[0],this.geom[1])},snap:function(t){var e=this.removeCommonBits(t),n=Ie.snap(e[0],e[1],this.snapTolerance);return n},interfaces_:function(){return[]},getClass:function(){return ri}}),ri.overlayOp=function(t,e,n){var i=new ri(t,e);return i.getResultGeometry(n)},ri.union=function(t,e){return ri.overlayOp(t,e,ii.UNION)},ri.intersection=function(t,e){return ri.overlayOp(t,e,ii.INTERSECTION)},ri.symDifference=function(t,e){return ri.overlayOp(t,e,ii.SYMDIFFERENCE)},ri.difference=function(t,e){return ri.overlayOp(t,e,ii.DIFFERENCE)},e(si.prototype,{getResultGeometry:function(t){var e=null,n=!1,i=null;try{e=ii.overlayOp(this.geom[0],this.geom[1],t);var r=!0;r&&(n=!0)}catch(s){if(!(s instanceof l))throw s;i=s}finally{}if(!n)try{e=ri.overlayOp(this.geom[0],this.geom[1],t)}catch(s){throw s instanceof l?i:s}finally{}return e},interfaces_:function(){return[]},getClass:function(){return si}}),si.overlayOp=function(t,e,n){var i=new si(t,e);return i.getResultGeometry(n)},si.union=function(t,e){return si.overlayOp(t,e,ii.UNION)},si.intersection=function(t,e){return si.overlayOp(t,e,ii.INTERSECTION)},si.symDifference=function(t,e){return si.overlayOp(t,e,ii.SYMDIFFERENCE)},si.difference=function(t,e){return si.overlayOp(t,e,ii.DIFFERENCE)},e(oi.prototype,{addPolygon:function(t){if(t.isEmpty())return null;var e=null,n=0,i=this.horizontalBisector(t);if(0===i.getLength())n=0,e=i.getCoordinate();else{var r=si.overlayOp(i,t,ii.INTERSECTION),s=this.widestGeometry(r);n=s.getEnvelopeInternal().getWidth(),e=oi.centre(s.getEnvelopeInternal())}(null===this.interiorPoint||n>this.maxWidth)&&(this.interiorPoint=e,this.maxWidth=n)},getInteriorPoint:function(){return this.interiorPoint},widestGeometry:function zo(){if(arguments[0]instanceof ft){var t=arguments[0];if(t.isEmpty())return t;for(var zo=t.getGeometryN(0),e=1;e<t.getNumGeometries();e++)t.getGeometryN(e).getEnvelopeInternal().getWidth()>zo.getEnvelopeInternal().getWidth()&&(zo=t.getGeometryN(e));return zo}if(arguments[0]instanceof B){var n=arguments[0];return n instanceof ft?this.widestGeometry(n):n}},horizontalBisector:function(t){var e=t.getEnvelopeInternal(),n=ai.getBisectorY(t);return this.factory.createLineString([new g(e.getMinX(),n),new g(e.getMaxX(),n)])},add:function(t){if(t instanceof Tt)this.addPolygon(t);else if(t instanceof ft)for(var e=t,n=0;n<e.getNumGeometries();n++)this.add(e.getGeometryN(n))},interfaces_:function(){return[]},getClass:function(){return oi}}),oi.centre=function(t){return new g(oi.avg(t.getMinX(),t.getMaxX()),oi.avg(t.getMinY(),t.getMaxY()))},oi.avg=function(t,e){return(t+e)/2},e(ai.prototype,{updateInterval:function(t){t<=this.centreY?t>this.loY&&(this.loY=t):t>this.centreY&&t<this.hiY&&(this.hiY=t)},getBisectorY:function(){this.process(this.poly.getExteriorRing());for(var t=0;t<this.poly.getNumInteriorRing();t++)this.process(this.poly.getInteriorRingN(t));var e=oi.avg(this.hiY,this.loY);return e},process:function(t){for(var e=t.getCoordinateSequence(),n=0;n<e.size();n++){var i=e.getY(n);this.updateInterval(i)}},interfaces_:function(){return[]},getClass:function(){return ai}}),ai.getBisectorY=function(t){var e=new ai(t);return e.getBisectorY()},oi.SafeBisectorFinder=ai,e(ui.prototype,{addEndpoints:function(){if(arguments[0]instanceof B){var t=arguments[0];if(t instanceof St)this.addEndpoints(t.getCoordinates());else if(t instanceof ft)for(var e=t,n=0;n<e.getNumGeometries();n++)this.addEndpoints(e.getGeometryN(n))}else if(arguments[0]instanceof Array){var i=arguments[0];this.add(i[0]),this.add(i[i.length-1])}},getInteriorPoint:function(){return this.interiorPoint},addInterior:function(){if(arguments[0]instanceof B){var t=arguments[0];if(t instanceof St)this.addInterior(t.getCoordinates());else if(t instanceof ft)for(var e=t,n=0;n<e.getNumGeometries();n++)this.addInterior(e.getGeometryN(n))}else if(arguments[0]instanceof Array)for(var i=arguments[0],n=1;n<i.length-1;n++)this.add(i[n])},add:function(t){var e=t.distance(this.centroid);e<this.minDistance&&(this.interiorPoint=new g(t),this.minDistance=e)},interfaces_:function(){return[]},getClass:function(){return ui}}),e(li.prototype,{getInteriorPoint:function(){return this.interiorPoint},add:function(){if(arguments[0]instanceof B){var t=arguments[0];if(t instanceof Lt)this.add(t.getCoordinate());else if(t instanceof ft)for(var e=t,n=0;n<e.getNumGeometries();n++)this.add(e.getGeometryN(n))}else if(arguments[0]instanceof g){var i=arguments[0],r=i.distance(this.centroid);r<this.minDistance&&(this.interiorPoint=new g(i),this.minDistance=r)}},interfaces_:function(){return[]},getClass:function(){return li}}),e(hi.prototype,{select:function(){if(1===arguments.length){arguments[0]}else if(2===arguments.length){var t=arguments[0],e=arguments[1];t.getLineSegment(e,this.selectedSegment),this.select(this.selectedSegment)}},interfaces_:function(){return[]},getClass:function(){return hi}}),e(ci.prototype,{hasChildren:function(){for(var t=0;2>t;t++)if(null!==this.subnode[t])return!0;return!1},isPrunable:function(){return!(this.hasChildren()||this.hasItems())},addAllItems:function(t){t.addAll(this.items);for(var e=0;2>e;e++)null!==this.subnode[e]&&this.subnode[e].addAllItems(t);return t},size:function(){for(var t=0,e=0;2>e;e++)null!==this.subnode[e]&&(t+=this.subnode[e].size());return t+this.items.size()},addAllItemsFromOverlapping:function(t,e){return null===t||this.isSearchMatch(t)?(e.addAll(this.items),null!==this.subnode[0]&&this.subnode[0].addAllItemsFromOverlapping(t,e),void(null!==this.subnode[1]&&this.subnode[1].addAllItemsFromOverlapping(t,e))):null},hasItems:function(){return!this.items.isEmpty()},remove:function(t,e){if(!this.isSearchMatch(t))return!1;for(var n=!1,i=0;2>i;i++)if(null!==this.subnode[i]&&(n=this.subnode[i].remove(t,e))){this.subnode[i].isPrunable()&&(this.subnode[i]=null);break}return n?n:n=this.items.remove(e)},getItems:function(){return this.items},depth:function(){for(var t=0,e=0;2>e;e++)if(null!==this.subnode[e]){var n=this.subnode[e].depth();n>t&&(t=n)}return t+1},nodeSize:function(){for(var t=0,e=0;2>e;e++)null!==this.subnode[e]&&(t+=this.subnode[e].nodeSize());return t+1},add:function(t){this.items.add(t)},interfaces_:function(){return[]},getClass:function(){return ci}}),ci.getSubnodeIndex=function(t,e){var n=-1;return t.min>=e&&(n=1),t.max<=e&&(n=0),n},e(fi.prototype,{expandToInclude:function(t){t.max>this.max&&(this.max=t.max),t.min<this.min&&(this.min=t.min)},getWidth:function(){return this.max-this.min},overlaps:function(){if(1===arguments.length){var t=arguments[0];return this.overlaps(t.min,t.max)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return!(this.min>n||this.max<e)}},getMin:function(){return this.min},toString:function(){return"["+this.min+", "+this.max+"]"},contains:function(){if(1===arguments.length){if(arguments[0]instanceof fi){var t=arguments[0];return this.contains(t.min,t.max)}if("number"==typeof arguments[0]){var e=arguments[0];return e>=this.min&&e<=this.max}}else if(2===arguments.length){var n=arguments[0],i=arguments[1];return n>=this.min&&i<=this.max}},init:function(t,e){this.min=t,this.max=e,t>e&&(this.min=e,this.max=t)},getMax:function(){return this.max},interfaces_:function(){return[]},getClass:function(){return fi}}),gi.exponent=function(t){return di(64,t)-1023},gi.powerOf2=function(t){return Math.pow(2,t)},e(pi.prototype,{getInterval:function(){return this.interval},getLevel:function(){return this.level},computeKey:function(t){for(this.level=pi.computeLevel(t),this.interval=new fi,this.computeInterval(this.level,t);!this.interval.contains(t);)this.level+=1,this.computeInterval(this.level,t)},computeInterval:function(t,e){var n=gi.powerOf2(t);this.pt=Math.floor(e.getMin()/n)*n,this.interval.init(this.pt,this.pt+n)},getPoint:function(){return this.pt},interfaces_:function(){return[]},getClass:function(){return pi}}),pi.computeLevel=function(t){var e=t.getWidth(),n=gi.exponent(e)+1;return n},h(vi,ci),e(vi.prototype,{getInterval:function(){return this.interval},find:function(t){var e=ci.getSubnodeIndex(t,this.centre);if(-1===e)return this;if(null!==this.subnode[e]){var n=this.subnode[e];return n.find(t)}return this},insert:function(t){f.isTrue(null===this.interval||this.interval.contains(t.interval));var e=ci.getSubnodeIndex(t.interval,this.centre);if(t.level===this.level-1)this.subnode[e]=t;else{var n=this.createSubnode(e);n.insert(t),this.subnode[e]=n}},isSearchMatch:function(t){return t.overlaps(this.interval)},getSubnode:function(t){return null===this.subnode[t]&&(this.subnode[t]=this.createSubnode(t)),this.subnode[t]},getNode:function(t){var e=ci.getSubnodeIndex(t,this.centre);if(-1!==e){var n=this.getSubnode(e);return n.getNode(t)}return this},createSubnode:function(t){var e=0,n=0;switch(t){case 0:e=this.interval.getMin(),n=this.centre;break;case 1:e=this.centre,n=this.interval.getMax()}var i=new fi(e,n),r=new vi(i,this.level-1);return r},interfaces_:function(){return[]},getClass:function(){return vi}}),vi.createNode=function(t){var e=new pi(t),n=new vi(e.getInterval(),e.getLevel());return n},vi.createExpanded=function(t,e){var n=new fi(e);null!==t&&n.expandToInclude(t.interval);var i=vi.createNode(n);return null!==t&&i.insert(t),i},e(mi.prototype,{interfaces_:function(){return[]},getClass:function(){return mi}}),mi.isZeroWidth=function(t,e){var n=e-t;if(0===n)return!0;var i=Math.max(Math.abs(t),Math.abs(e)),r=n/i,s=gi.exponent(r);return s<=mi.MIN_BINARY_EXPONENT},mi.MIN_BINARY_EXPONENT=-50,h(yi,ci),e(yi.prototype,{insert:function(t,e){var n=ci.getSubnodeIndex(t,yi.origin);if(-1===n)return this.add(e),null;var i=this.subnode[n];if(null===i||!i.getInterval().contains(t)){var r=vi.createExpanded(i,t);this.subnode[n]=r}this.insertContained(this.subnode[n],t,e)},isSearchMatch:function(t){return!0},insertContained:function(t,e,n){f.isTrue(t.getInterval().contains(e));var i=mi.isZeroWidth(e.getMin(),e.getMax()),r=null;r=i?t.find(e):t.getNode(e),r.add(n)},interfaces_:function(){return[]},getClass:function(){return yi}}),yi.origin=0,e(xi.prototype,{size:function(){return null!==this.root?this.root.size():0},insert:function(t,e){this.collectStats(t);var n=xi.ensureExtent(t,this.minExtent);this.root.insert(n,e)},query:function(){if(1===arguments.length){if("number"==typeof arguments[0]){var t=arguments[0];return this.query(new fi(t,t))}if(arguments[0]instanceof fi){var e=arguments[0],n=new I;return this.query(e,n),n}}else if(2===arguments.length){var i=arguments[0],r=arguments[1];this.root.addAllItemsFromOverlapping(i,r)}},iterator:function(){var t=new I;return this.root.addAllItems(t),t.iterator()},remove:function(t,e){var n=xi.ensureExtent(t,this.minExtent);return this.root.remove(n,e)},collectStats:function(t){var e=t.getWidth();e<this.minExtent&&e>0&&(this.minExtent=e)},depth:function(){return null!==this.root?this.root.depth():0},nodeSize:function(){return null!==this.root?this.root.nodeSize():0},interfaces_:function(){return[]},getClass:function(){return xi}}),xi.ensureExtent=function(t,e){var n=t.getMin(),i=t.getMax();return n!==i?t:(n===i&&(n-=e/2,i=n+e/2),new fi(n,i))},e(Ei.prototype,{isInside:function(t){},interfaces_:function(){return[]},getClass:function(){return Ei}}),e(Ii.prototype,{testLineSegment:function(t,e){var n=null,i=null,r=null,s=null,o=null,a=e.p0,u=e.p1;i=a.x-t.x,r=a.y-t.y,s=u.x-t.x,o=u.y-t.y,(r>0&&0>=o||o>0&&0>=r)&&(n=ue.signOfDet2x2(i,r,s,o)/(o-r),n>0&&this.crossings++)},buildIndex:function(){this.tree=new xi;for(var t=H.removeRepeatedPoints(this.ring.getCoordinates()),e=$e.getChains(t),n=0;n<e.size();n++){var i=e.get(n),r=i.getEnvelope();this.interval.min=r.getMinY(),this.interval.max=r.getMaxY(),this.tree.insert(this.interval,i)}},testMonotoneChain:function(t,e,n){n.select(t,e)},isInside:function(t){this.crossings=0;var e=new C(r.NEGATIVE_INFINITY,r.POSITIVE_INFINITY,t.y,t.y);this.interval.min=t.y,this.interval.max=t.y;for(var n=this.tree.query(this.interval),i=new Ni(this,t),s=n.iterator();s.hasNext();){var o=s.next();this.testMonotoneChain(e,i,o)}return this.crossings%2===1},interfaces_:function(){return[Ei]},getClass:function(){return Ii}}),h(Ni,hi),e(Ni.prototype,{select:function(){if(1!==arguments.length)return hi.prototype.select.apply(this,arguments);var t=arguments[0];this.mcp.testLineSegment(this.p,t)},interfaces_:function(){return[]},getClass:function(){return Ni}}),Ii.MCSelecter=Ni,e(Ci.prototype,{interfaces_:function(){return[]},getClass:function(){return Ci}}),Ci.toDegrees=function(t){return 180*t/Math.PI},Ci.normalize=function(t){for(;t>Math.PI;)t-=Ci.PI_TIMES_2;for(;t<=-Math.PI;)t+=Ci.PI_TIMES_2;return t},Ci.angle=function(){if(1===arguments.length){var t=arguments[0];return Math.atan2(t.y,t.x)}if(2===arguments.length){var e=arguments[0],n=arguments[1],i=n.x-e.x,r=n.y-e.y;return Math.atan2(r,i)}},Ci.isAcute=function(t,e,n){var i=t.x-e.x,r=t.y-e.y,s=n.x-e.x,o=n.y-e.y,a=i*s+r*o;return a>0},Ci.isObtuse=function(t,e,n){var i=t.x-e.x,r=t.y-e.y,s=n.x-e.x,o=n.y-e.y,a=i*s+r*o;return 0>a},Ci.interiorAngle=function(t,e,n){var i=Ci.angle(e,t),r=Ci.angle(e,n);return Math.abs(r-i)},Ci.normalizePositive=function(t){if(0>t){for(;0>t;)t+=Ci.PI_TIMES_2;t>=Ci.PI_TIMES_2&&(t=0)}else{for(;t>=Ci.PI_TIMES_2;)t-=Ci.PI_TIMES_2;0>t&&(t=0)}return t},Ci.angleBetween=function(t,e,n){var i=Ci.angle(e,t),r=Ci.angle(e,n);return Ci.diff(i,r)},Ci.diff=function(t,e){var n=null;return n=e>t?e-t:t-e,n>Math.PI&&(n=2*Math.PI-n),n},Ci.toRadians=function(t){return t*Math.PI/180},Ci.getTurn=function(t,e){var n=Math.sin(e-t);return n>0?Ci.COUNTERCLOCKWISE:0>n?Ci.CLOCKWISE:Ci.NONE},Ci.angleBetweenOriented=function(t,e,n){var i=Ci.angle(e,t),r=Ci.angle(e,n),s=r-i;return s<=-Math.PI?s+Ci.PI_TIMES_2:s>Math.PI?s-Ci.PI_TIMES_2:s},Ci.PI_TIMES_2=2*Math.PI,Ci.PI_OVER_2=Math.PI/2,Ci.PI_OVER_4=Math.PI/4,Ci.COUNTERCLOCKWISE=he.COUNTERCLOCKWISE,Ci.CLOCKWISE=he.CLOCKWISE,Ci.NONE=he.COLLINEAR,e(Si.prototype,{area:function(){return Si.area(this.p0,this.p1,this.p2)},signedArea:function(){return Si.signedArea(this.p0,this.p1,this.p2)},interpolateZ:function(t){if(null===t)throw new i("Supplied point is null.");return Si.interpolateZ(t,this.p0,this.p1,this.p2)},longestSideLength:function(){return Si.longestSideLength(this.p0,this.p1,this.p2)},isAcute:function(){return Si.isAcute(this.p0,this.p1,this.p2)},circumcentre:function(){return Si.circumcentre(this.p0,this.p1,this.p2)},area3D:function(){return Si.area3D(this.p0,this.p1,this.p2)},centroid:function(){return Si.centroid(this.p0,this.p1,this.p2)},inCentre:function(){return Si.inCentre(this.p0,this.p1,this.p2)},interfaces_:function(){return[]},getClass:function(){return Si}}),Si.area=function(t,e,n){return Math.abs(((n.x-t.x)*(e.y-t.y)-(e.x-t.x)*(n.y-t.y))/2)},Si.signedArea=function(t,e,n){return((n.x-t.x)*(e.y-t.y)-(e.x-t.x)*(n.y-t.y))/2},Si.det=function(t,e,n,i){return t*i-e*n},Si.interpolateZ=function(t,e,n,i){var r=e.x,s=e.y,o=n.x-r,a=i.x-r,u=n.y-s,l=i.y-s,h=o*l-a*u,c=t.x-r,f=t.y-s,g=(l*c-a*f)/h,d=(-u*c+o*f)/h,p=e.z+g*(n.z-e.z)+d*(i.z-e.z);return p},Si.longestSideLength=function(t,e,n){var i=t.distance(e),r=e.distance(n),s=n.distance(t),o=i;return r>o&&(o=r),s>o&&(o=s),o},Si.isAcute=function(t,e,n){return Ci.isAcute(t,e,n)&&Ci.isAcute(e,n,t)?!!Ci.isAcute(n,t,e):!1},Si.circumcentre=function(t,e,n){var i=n.x,r=n.y,s=t.x-i,o=t.y-r,a=e.x-i,u=e.y-r,l=2*Si.det(s,o,a,u),h=Si.det(o,s*s+o*o,u,a*a+u*u),c=Si.det(s,s*s+o*o,a,a*a+u*u),f=i-h/l,d=r+c/l;return new g(f,d)},Si.perpendicularBisector=function(t,e){var n=e.x-t.x,i=e.y-t.y,r=new F(t.x+n/2,t.y+i/2,1),s=new F(t.x-i+n/2,t.y+n+i/2,1);return new F(r,s)},Si.angleBisector=function(t,e,n){var i=e.distance(t),r=e.distance(n),s=i/(i+r),o=n.x-t.x,a=n.y-t.y,u=new g(t.x+s*o,t.y+s*a);return u},Si.area3D=function(t,e,n){var i=e.x-t.x,r=e.y-t.y,s=e.z-t.z,o=n.x-t.x,a=n.y-t.y,u=n.z-t.z,l=r*u-s*a,h=s*o-i*u,c=i*a-r*o,f=l*l+h*h+c*c,g=Math.sqrt(f)/2;return g},Si.centroid=function(t,e,n){var i=(t.x+e.x+n.x)/3,r=(t.y+e.y+n.y)/3;return new g(i,r)},Si.inCentre=function(t,e,n){var i=e.distance(n),r=t.distance(n),s=t.distance(e),o=i+r+s,a=(i*t.x+r*e.x+s*n.x)/o,u=(i*t.y+r*e.y+s*n.y)/o;return new g(a,u)},e(wi.prototype,{getRadius:function(){return this.compute(),this.radius},getDiameter:function(){switch(this.compute(),this.extremalPts.length){case 0:return this.input.getFactory().createLineString();case 1:return this.input.getFactory().createPoint(this.centre)}var t=this.extremalPts[0],e=this.extremalPts[1];return this.input.getFactory().createLineString([t,e])},getExtremalPoints:function(){return this.compute(),this.extremalPts},computeCirclePoints:function(){if(this.input.isEmpty())return this.extremalPts=new Array(0).fill(null),null;if(1===this.input.getNumPoints()){var t=this.input.getCoordinates();return this.extremalPts=[new g(t[0])],null}var e=this.input.convexHull(),n=e.getCoordinates(),t=n;if(n[0].equals2D(n[n.length-1])&&(t=new Array(n.length-1).fill(null),H.copyDeep(n,0,t,0,n.length-1)),t.length<=2)return this.extremalPts=H.copyDeep(t),null;for(var i=wi.lowestPoint(t),r=wi.pointWitMinAngleWithX(t,i),s=0;s<t.length;s++){var o=wi.pointWithMinAngleWithSegment(t,i,r);if(Ci.isObtuse(i,o,r))return this.extremalPts=[new g(i),new g(r)],null;if(Ci.isObtuse(o,i,r))i=o;else{if(!Ci.isObtuse(o,r,i))return this.extremalPts=[new g(i),new g(r),new g(o)],null;r=o}}f.shouldNeverReachHere("Logic failure in Minimum Bounding Circle algorithm!")},compute:function(){return null!==this.extremalPts?null:(this.computeCirclePoints(),this.computeCentre(),void(null!==this.centre&&(this.radius=this.centre.distance(this.extremalPts[0]))))},getFarthestPoints:function(){switch(this.compute(),this.extremalPts.length){case 0:return this.input.getFactory().createLineString();case 1:return this.input.getFactory().createPoint(this.centre)}var t=this.extremalPts[0],e=this.extremalPts[this.extremalPts.length-1];return this.input.getFactory().createLineString([t,e])},getCircle:function(){if(this.compute(),null===this.centre)return this.input.getFactory().createPolygon();var t=this.input.getFactory().createPoint(this.centre);return 0===this.radius?t:t.buffer(this.radius)},getCentre:function(){return this.compute(),this.centre},computeCentre:function(){switch(this.extremalPts.length){case 0:this.centre=null;break;case 1:this.centre=this.extremalPts[0];break;case 2:this.centre=new g((this.extremalPts[0].x+this.extremalPts[1].x)/2,(this.extremalPts[0].y+this.extremalPts[1].y)/2);break;case 3:this.centre=Si.circumcentre(this.extremalPts[0],this.extremalPts[1],this.extremalPts[2])}},interfaces_:function(){return[]},getClass:function(){return wi}}),wi.pointWitMinAngleWithX=function(t,e){for(var n=r.MAX_VALUE,i=null,s=0;s<t.length;s++){var o=t[s];if(o!==e){var a=o.x-e.x,u=o.y-e.y;0>u&&(u=-u);var l=Math.sqrt(a*a+u*u),h=u/l;n>h&&(n=h,i=o)}}return i},wi.lowestPoint=function(t){for(var e=t[0],n=1;n<t.length;n++)t[n].y<e.y&&(e=t[n]);return e},wi.pointWithMinAngleWithSegment=function(t,e,n){for(var i=r.MAX_VALUE,s=null,o=0;o<t.length;o++){var a=t[o];if(a!==e&&a!==n){var u=Ci.angleBetween(e,a,n);i>u&&(i=u,s=a)}}return s},e(Li.prototype,{getWidthCoordinate:function(){return this.computeMinimumDiameter(),this.minWidthPt},getSupportingSegment:function(){return this.computeMinimumDiameter(),this.inputGeom.getFactory().createLineString([this.minBaseSeg.p0,this.minBaseSeg.p1])},getDiameter:function(){if(this.computeMinimumDiameter(),null===this.minWidthPt)return this.inputGeom.getFactory().createLineString(null);var t=this.minBaseSeg.project(this.minWidthPt);return this.inputGeom.getFactory().createLineString([t,this.minWidthPt])},computeWidthConvex:function(t){t instanceof Tt?this.convexHullPts=t.getExteriorRing().getCoordinates():this.convexHullPts=t.getCoordinates(),0===this.convexHullPts.length?(this.minWidth=0,this.minWidthPt=null,this.minBaseSeg=null):1===this.convexHullPts.length?(this.minWidth=0,this.minWidthPt=this.convexHullPts[0],this.minBaseSeg.p0=this.convexHullPts[0],this.minBaseSeg.p1=this.convexHullPts[0]):2===this.convexHullPts.length||3===this.convexHullPts.length?(this.minWidth=0,this.minWidthPt=this.convexHullPts[0],this.minBaseSeg.p0=this.convexHullPts[0],this.minBaseSeg.p1=this.convexHullPts[1]):this.computeConvexRingMinDiameter(this.convexHullPts)},computeConvexRingMinDiameter:function(t){this.minWidth=r.MAX_VALUE;for(var e=1,n=new ce,i=0;i<t.length-1;i++)n.p0=t[i],n.p1=t[i+1],e=this.findMaxPerpDistance(t,n,e)},computeMinimumDiameter:function(){if(null!==this.minWidthPt)return null;if(this.isConvex)this.computeWidthConvex(this.inputGeom);else{var t=new me(this.inputGeom).getConvexHull();this.computeWidthConvex(t)}},getLength:function(){return this.computeMinimumDiameter(),this.minWidth},findMaxPerpDistance:function(t,e,n){for(var i=e.distancePerpendicular(t[n]),r=i,s=n,o=s;r>=i;)i=r,s=o,o=Li.nextIndex(t,s),r=e.distancePerpendicular(t[o]);return i<this.minWidth&&(this.minPtIndex=s,this.minWidth=i,this.minWidthPt=t[this.minPtIndex],this.minBaseSeg=new ce(e)),s},getMinimumRectangle:function(){if(this.computeMinimumDiameter(),0===this.minWidth)return this.minBaseSeg.p0.equals2D(this.minBaseSeg.p1)?this.inputGeom.getFactory().createPoint(this.minBaseSeg.p0):this.minBaseSeg.toGeometry(this.inputGeom.getFactory());for(var t=this.minBaseSeg.p1.x-this.minBaseSeg.p0.x,e=this.minBaseSeg.p1.y-this.minBaseSeg.p0.y,n=r.MAX_VALUE,i=-r.MAX_VALUE,s=r.MAX_VALUE,o=-r.MAX_VALUE,a=0;a<this.convexHullPts.length;a++){var u=Li.computeC(t,e,this.convexHullPts[a]);u>i&&(i=u),n>u&&(n=u);var l=Li.computeC(-e,t,this.convexHullPts[a]);l>o&&(o=l),s>l&&(s=l)}var h=Li.computeSegmentForLine(-t,-e,o),c=Li.computeSegmentForLine(-t,-e,s),f=Li.computeSegmentForLine(-e,t,i),g=Li.computeSegmentForLine(-e,t,n),d=f.lineIntersection(h),p=g.lineIntersection(h),v=g.lineIntersection(c),m=f.lineIntersection(c),y=this.inputGeom.getFactory().createLinearRing([d,p,v,m,d]);return this.inputGeom.getFactory().createPolygon(y,null)},interfaces_:function(){return[]},getClass:function(){return Li}}),Li.nextIndex=function(t,e){return e++,e>=t.length&&(e=0),e},Li.computeC=function(t,e,n){return t*n.y-e*n.x},Li.getMinimumDiameter=function(t){return new Li(t).getDiameter()},Li.getMinimumRectangle=function(t){return new Li(t).getMinimumRectangle()},Li.computeSegmentForLine=function(t,e,n){var i=null,r=null;return Math.abs(e)>Math.abs(t)?(i=new g(0,n/e),r=new g(1,n/e-t/e)):(i=new g(n/t,0),r=new g(n/t-e/t,1)),new ce(i,r)};var co=Object.freeze({Centroid:ge,CGAlgorithms:he,ConvexHull:me,InteriorPointArea:oi,InteriorPointLine:ui,InteriorPointPoint:li,RobustLineIntersector:ae,MCPointInRing:Ii,MinimumBoundingCircle:wi,MinimumDiameter:Li});e(Ri.prototype,{getResultGeometry:function(){return new Ti(this.distanceTolerance).transform(this.inputGeom)},setDistanceTolerance:function(t){if(0>=t)throw new i("Tolerance must be positive");this.distanceTolerance=t},interfaces_:function(){return[]},getClass:function(){return Ri}}),Ri.densifyPoints=function(t,e,n){for(var i=new ce,r=new N,s=0;s<t.length-1;s++){i.p0=t[s],i.p1=t[s+1],r.add(i.p0,!1);var o=i.getLength(),a=Math.trunc(o/e)+1;if(a>1)for(var u=o/a,l=1;a>l;l++){var h=l*u/o,c=i.pointAlong(h);n.makePrecise(c),r.add(c,!1)}}return r.add(t[t.length-1],!1),r.toCoordinateArray()},Ri.densify=function(t,e){var n=new Ri(t);return n.setDistanceTolerance(e),n.getResultGeometry()},h(Ti,xe),e(Ti.prototype,{transformMultiPolygon:function(t,e){var n=xe.prototype.transformMultiPolygon.call(this,t,e);return this.createValidArea(n)},transformPolygon:function(t,e){var n=xe.prototype.transformPolygon.call(this,t,e);return e instanceof Ot?n:this.createValidArea(n)},transformCoordinates:function(t,e){var n=t.toCoordinateArray(),i=Ri.densifyPoints(n,this.distanceTolerance,e.getPrecisionModel());return e instanceof St&&1===i.length&&(i=new Array(0).fill(null)),this.factory.getCoordinateSequenceFactory().create(i)},createValidArea:function(t){return t.buffer(0)},interfaces_:function(){return[]},getClass:function(){return Ti}}),Ri.DensifyTransformer=Ti;var fo=Object.freeze({Densifier:Ri});e(Pi.prototype,{find:function(t){var e=this;do{if(null===e)return null;if(e.dest().equals2D(t))return e;e=e.oNext()}while(e!==this);return null},dest:function(){return this._sym._orig},oNext:function(){return this._sym._next},insert:function(t){if(this.oNext()===this)return this.insertAfter(t),null;var e=this.compareTo(t),n=this;do{var i=n.oNext(),r=i.compareTo(t);if(r!==e||i===this)return n.insertAfter(t),null;n=i}while(n!==this);f.shouldNeverReachHere()},insertAfter:function(t){f.equals(this._orig,t.orig());var e=this.oNext();this._sym.setNext(t),t.sym().setNext(e)},degree:function Vo(){var Vo=0,t=this;do Vo++,t=t.oNext();while(t!==this);return Vo},equals:function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this._orig.equals2D(t)&&this._sym._orig.equals(e)}},deltaY:function(){return this._sym._orig.y-this._orig.y},sym:function(){return this._sym},prev:function(){return this._sym.next()._sym},compareAngularDirection:function(t){var e=this.deltaX(),n=this.deltaY(),i=t.deltaX(),r=t.deltaY();if(e===i&&n===r)return 0;var s=Je.quadrant(e,n),o=Je.quadrant(i,r);return s>o?1:o>s?-1:he.computeOrientation(t._orig,t.dest(),this.dest())},prevNode:function(){for(var t=this;2===t.degree();)if(t=t.prev(),t===this)return null;return t},compareTo:function(t){var e=t,n=this.compareAngularDirection(e);return n},next:function(){return this._next},setSym:function(t){this._sym=t},orig:function(){return this._orig},toString:function(){return"HE("+this._orig.x+" "+this._orig.y+", "+this._sym._orig.x+" "+this._sym._orig.y+")"},setNext:function(t){this._next=t},init:function(t){this.setSym(t),t.setSym(this),this.setNext(t),t.setNext(this)},deltaX:function(){return this._sym._orig.x-this._orig.x},interfaces_:function(){return[]},getClass:function(){return Pi}}),Pi.init=function(t,e){if(null!==t._sym||null!==e._sym||null!==t._next||null!==e._next)throw new IllegalStateException("Edges are already initialized");return t.init(e),t},Pi.create=function(t,e){var n=new Pi(t),i=new Pi(e);return n.init(i),n},h(bi,Pi),e(bi.prototype,{mark:function(){this._isMarked=!0},setMark:function(t){this._isMarked=t},isMarked:function(){return this._isMarked},interfaces_:function(){return[]},getClass:function(){return bi}}),bi.setMarkBoth=function(t,e){t.setMark(e),t.sym().setMark(e)},bi.isMarked=function(t){return t.isMarked()},bi.setMark=function(t,e){t.setMark(e)},bi.markBoth=function(t){t.mark(),t.sym().mark()},bi.mark=function(t){t.mark()},e(Oi.prototype,{insert:function(t,e,n){var i=this.create(t,e);null!==n?n.insert(i):this.vertexMap.put(t,i);var r=this.vertexMap.get(e);return null!==r?r.insert(i.sym()):this.vertexMap.put(e,i.sym()),i},create:function(t,e){var n=this.createEdge(t),i=this.createEdge(e);return Pi.init(n,i),n},createEdge:function(t){return new Pi(t)},addEdge:function(t,e){if(!Oi.isValidEdge(t,e))return null;var n=this.vertexMap.get(t),i=null;if(null!==n&&(i=n.find(e)),null!==i)return i;var r=this.insert(t,e,n);return r},getVertexEdges:function(){return this.vertexMap.values()},findEdge:function(t,e){var n=this.vertexMap.get(t);return null===n?null:n.find(e)},interfaces_:function(){return[]},getClass:function(){return Oi}}),Oi.isValidEdge=function(t,e){var n=e.compareTo(t);return 0!==n},h(_i,bi),e(_i.prototype,{setStart:function(){this._isStart=!0;
},isStart:function(){return this._isStart},interfaces_:function(){return[]},getClass:function(){return _i}}),h(Mi,Oi),e(Mi.prototype,{createEdge:function(t){return new _i(t)},interfaces_:function(){return[]},getClass:function(){return Mi}}),e(Di.prototype,{addLine:function(t){this.lines.add(this.factory.createLineString(t.toCoordinateArray()))},updateRingStartEdge:function(t){return t.isStart()||(t=t.sym(),t.isStart())?null===this.ringStartEdge?(this.ringStartEdge=t,null):void(t.orig().compareTo(this.ringStartEdge.orig())<0&&(this.ringStartEdge=t)):null},getResult:function(){return null===this.result&&this.computeResult(),this.result},process:function(t){var e=t.prevNode();null===e&&(e=t),this.stackEdges(e),this.buildLines()},buildRing:function(t){var e=new N,n=t;for(e.add(n.orig().copy(),!1);2===n.sym().degree();){var i=n.next();if(i===t)break;e.add(i.orig().copy(),!1),n=i}e.add(n.dest().copy(),!1),this.addLine(e)},buildLine:function(t){var e=new N,n=t;for(this.ringStartEdge=null,bi.markBoth(n),e.add(n.orig().copy(),!1);2===n.sym().degree();){this.updateRingStartEdge(n);var i=n.next();if(i===t)return this.buildRing(this.ringStartEdge),null;e.add(i.orig().copy(),!1),n=i,bi.markBoth(n)}e.add(n.dest().copy(),!1),this.stackEdges(n.sym()),this.addLine(e)},stackEdges:function(t){var e=t;do bi.isMarked(e)||this.nodeEdgeStack.add(e),e=e.oNext();while(e!==t)},computeResult:function(){for(var t=this.graph.getVertexEdges(),e=t.iterator();e.hasNext();){var n=e.next();bi.isMarked(n)||this.process(n)}this.result=this.factory.buildGeometry(this.lines)},buildLines:function(){for(;!this.nodeEdgeStack.empty();){var t=this.nodeEdgeStack.pop();bi.isMarked(t)||this.buildLine(t)}},add:function(){if(arguments[0]instanceof B){var t=arguments[0];t.apply({interfaces_:function(){return[q]},filter:function(t){t instanceof St&&this.add(t)}})}else if(R(arguments[0],v))for(var e=arguments[0],n=e.iterator();n.hasNext();){var i=n.next();this.add(i)}else if(arguments[0]instanceof St){var r=arguments[0];null===this.factory&&(this.factory=r.getFactory());for(var s=r.getCoordinateSequence(),o=!1,n=1;n<s.size();n++){var a=this.graph.addEdge(s.getCoordinate(n-1),s.getCoordinate(n));null!==a&&(o||(a.setStart(),o=!0))}}},interfaces_:function(){return[]},getClass:function(){return Di}}),Di.dissolve=function(t){var e=new Di;return e.add(t),e.getResult()};var go=Object.freeze({LineDissolver:Di}),po=Object.freeze({GeometryGraph:$n});e(Ai.prototype,{hasChildren:function(){for(var t=0;4>t;t++)if(null!==this.subnode[t])return!0;return!1},isPrunable:function(){return!(this.hasChildren()||this.hasItems())},addAllItems:function(t){t.addAll(this.items);for(var e=0;4>e;e++)null!==this.subnode[e]&&this.subnode[e].addAllItems(t);return t},getNodeCount:function(){for(var t=0,e=0;4>e;e++)null!==this.subnode[e]&&(t+=this.subnode[e].size());return t+1},size:function(){for(var t=0,e=0;4>e;e++)null!==this.subnode[e]&&(t+=this.subnode[e].size());return t+this.items.size()},addAllItemsFromOverlapping:function(t,e){if(!this.isSearchMatch(t))return null;e.addAll(this.items);for(var n=0;4>n;n++)null!==this.subnode[n]&&this.subnode[n].addAllItemsFromOverlapping(t,e)},visitItems:function(t,e){for(var n=this.items.iterator();n.hasNext();)e.visitItem(n.next())},hasItems:function(){return!this.items.isEmpty()},remove:function(t,e){if(!this.isSearchMatch(t))return!1;for(var n=!1,i=0;4>i;i++)if(null!==this.subnode[i]&&(n=this.subnode[i].remove(t,e))){this.subnode[i].isPrunable()&&(this.subnode[i]=null);break}return n?n:n=this.items.remove(e)},visit:function(t,e){if(!this.isSearchMatch(t))return null;this.visitItems(t,e);for(var n=0;4>n;n++)null!==this.subnode[n]&&this.subnode[n].visit(t,e)},getItems:function(){return this.items},depth:function(){for(var t=0,e=0;4>e;e++)if(null!==this.subnode[e]){var n=this.subnode[e].depth();n>t&&(t=n)}return t+1},isEmpty:function ko(){var ko=!0;this.items.isEmpty()||(ko=!1);for(var t=0;4>t;t++)null!==this.subnode[t]&&(this.subnode[t].isEmpty()||(ko=!1));return ko},add:function(t){this.items.add(t)},interfaces_:function(){return[u]},getClass:function(){return Ai}}),Ai.getSubnodeIndex=function(t,e,n){var i=-1;return t.getMinX()>=e&&(t.getMinY()>=n&&(i=3),t.getMaxY()<=n&&(i=1)),t.getMaxX()<=e&&(t.getMinY()>=n&&(i=2),t.getMaxY()<=n&&(i=0)),i},e(Fi.prototype,{getLevel:function(){return this.level},computeKey:function(){if(1===arguments.length){var t=arguments[0];for(this.level=Fi.computeQuadLevel(t),this.env=new C,this.computeKey(this.level,t);!this.env.contains(t);)this.level+=1,this.computeKey(this.level,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1],i=gi.powerOf2(e);this.pt.x=Math.floor(n.getMinX()/i)*i,this.pt.y=Math.floor(n.getMinY()/i)*i,this.env.init(this.pt.x,this.pt.x+i,this.pt.y,this.pt.y+i)}},getEnvelope:function(){return this.env},getCentre:function(){return new g((this.env.getMinX()+this.env.getMaxX())/2,(this.env.getMinY()+this.env.getMaxY())/2)},getPoint:function(){return this.pt},interfaces_:function(){return[]},getClass:function(){return Fi}}),Fi.computeQuadLevel=function(t){var e=t.getWidth(),n=t.getHeight(),i=e>n?e:n,r=gi.exponent(i)+1;return r},h(Gi,Ai),e(Gi.prototype,{find:function(t){var e=Ai.getSubnodeIndex(t,this.centrex,this.centrey);if(-1===e)return this;if(null!==this.subnode[e]){var n=this.subnode[e];return n.find(t)}return this},isSearchMatch:function(t){return this.env.intersects(t)},getSubnode:function(t){return null===this.subnode[t]&&(this.subnode[t]=this.createSubnode(t)),this.subnode[t]},getEnvelope:function(){return this.env},getNode:function(t){var e=Ai.getSubnodeIndex(t,this.centrex,this.centrey);if(-1!==e){var n=this.getSubnode(e);return n.getNode(t)}return this},createSubnode:function(t){var e=0,n=0,i=0,r=0;switch(t){case 0:e=this.env.getMinX(),n=this.centrex,i=this.env.getMinY(),r=this.centrey;break;case 1:e=this.centrex,n=this.env.getMaxX(),i=this.env.getMinY(),r=this.centrey;break;case 2:e=this.env.getMinX(),n=this.centrex,i=this.centrey,r=this.env.getMaxY();break;case 3:e=this.centrex,n=this.env.getMaxX(),i=this.centrey,r=this.env.getMaxY()}var s=new C(e,n,i,r),o=new Gi(s,this.level-1);return o},insertNode:function(t){f.isTrue(null===this.env||this.env.contains(t.env));var e=Ai.getSubnodeIndex(t.env,this.centrex,this.centrey);if(t.level===this.level-1)this.subnode[e]=t;else{var n=this.createSubnode(e);n.insertNode(t),this.subnode[e]=n}},interfaces_:function(){return[]},getClass:function(){return Gi}}),Gi.createNode=function(t){var e=new Fi(t),n=new Gi(e.getEnvelope(),e.getLevel());return n},Gi.createExpanded=function(t,e){var n=new C(e);null!==t&&n.expandToInclude(t.env);var i=Gi.createNode(n);return null!==t&&i.insertNode(t),i},h(qi,Ai),e(qi.prototype,{insert:function(t,e){var n=Ai.getSubnodeIndex(t,qi.origin.x,qi.origin.y);if(-1===n)return this.add(e),null;var i=this.subnode[n];if(null===i||!i.getEnvelope().contains(t)){var r=Gi.createExpanded(i,t);this.subnode[n]=r}this.insertContained(this.subnode[n],t,e)},isSearchMatch:function(t){return!0},insertContained:function(t,e,n){f.isTrue(t.getEnvelope().contains(e));var i=mi.isZeroWidth(e.getMinX(),e.getMaxX()),r=mi.isZeroWidth(e.getMinY(),e.getMaxY()),s=null;s=i||r?t.find(e):t.getNode(e),s.add(n)},interfaces_:function(){return[]},getClass:function(){return qi}}),qi.origin=new g(0,0),e(Bi.prototype,{size:function(){return null!==this.root?this.root.size():0},insert:function(t,e){this.collectStats(t);var n=Bi.ensureExtent(t,this.minExtent);this.root.insert(n,e)},query:function(){if(1===arguments.length){var t=arguments[0],e=new Yn;return this.query(t,e),e.getItems()}if(2===arguments.length){var n=arguments[0],i=arguments[1];this.root.visit(n,i)}},queryAll:function(){var t=new I;return this.root.addAllItems(t),t},remove:function(t,e){var n=Bi.ensureExtent(t,this.minExtent);return this.root.remove(n,e)},collectStats:function(t){var e=t.getWidth();e<this.minExtent&&e>0&&(this.minExtent=e);var n=t.getHeight();n<this.minExtent&&n>0&&(this.minExtent=n)},depth:function(){return null!==this.root?this.root.depth():0},isEmpty:function(){return null===this.root},interfaces_:function(){return[Fe,u]},getClass:function(){return Bi}}),Bi.ensureExtent=function(t,e){var n=t.getMinX(),i=t.getMaxX(),r=t.getMinY(),s=t.getMaxY();return n!==i&&r!==s?t:(n===i&&(n-=e/2,i=n+e/2),r===s&&(r-=e/2,s=r+e/2),new C(n,i,r,s))},Bi.serialVersionUID=-0x678b60c967a25400;var vo=Object.freeze({Quadtree:Bi}),mo=Object.freeze({STRtree:ke}),yo=Object.freeze({quadtree:vo,strtree:mo}),xo=["Point","MultiPoint","LineString","MultiLineString","Polygon","MultiPolygon"];e(zi.prototype,{read:function(t){var e=void 0;e="string"==typeof t?JSON.parse(t):t;var n=e.type;if(!Eo[n])throw new Error("Unknown GeoJSON type: "+e.type);return-1!==xo.indexOf(n)?Eo[n].apply(this,[e.coordinates]):"GeometryCollection"===n?Eo[n].apply(this,[e.geometries]):Eo[n].apply(this,[e])},write:function(t){var e=t.getGeometryType();if(!Io[e])throw new Error("Geometry is not supported");return Io[e].apply(this,[t])}});var Eo={Feature:function(t){var e={};for(var n in t)e[n]=t[n];if(t.geometry){var i=t.geometry.type;if(!Eo[i])throw new Error("Unknown GeoJSON type: "+t.type);e.geometry=this.read(t.geometry)}return t.bbox&&(e.bbox=Eo.bbox.apply(this,[t.bbox])),e},FeatureCollection:function(t){var e={};if(t.features){e.features=[];for(var n=0;n<t.features.length;++n)e.features.push(this.read(t.features[n]))}return t.bbox&&(e.bbox=this.parse.bbox.apply(this,[t.bbox])),e},coordinates:function Yo(t){for(var Yo=[],e=0;e<t.length;++e){var n=t[e];Yo.push(new g(n[0],n[1]))}return Yo},bbox:function(t){return this.geometryFactory.createLinearRing([new g(t[0],t[1]),new g(t[2],t[1]),new g(t[2],t[3]),new g(t[0],t[3]),new g(t[0],t[1])])},Point:function(t){var e=new g(t[0],t[1]);return this.geometryFactory.createPoint(e)},MultiPoint:function(t){for(var e=[],n=0;n<t.length;++n)e.push(Eo.Point.apply(this,[t[n]]));return this.geometryFactory.createMultiPoint(e)},LineString:function(t){var e=Eo.coordinates.apply(this,[t]);return this.geometryFactory.createLineString(e)},MultiLineString:function(t){for(var e=[],n=0;n<t.length;++n)e.push(Eo.LineString.apply(this,[t[n]]));return this.geometryFactory.createMultiLineString(e)},Polygon:function(t){for(var e=Eo.coordinates.apply(this,[t[0]]),n=this.geometryFactory.createLinearRing(e),i=[],r=1;r<t.length;++r){var s=t[r],o=Eo.coordinates.apply(this,[s]),a=this.geometryFactory.createLinearRing(o);i.push(a)}return this.geometryFactory.createPolygon(n,i)},MultiPolygon:function(t){for(var e=[],n=0;n<t.length;++n){var i=t[n];e.push(Eo.Polygon.apply(this,[i]))}return this.geometryFactory.createMultiPolygon(e)},GeometryCollection:function(t){for(var e=[],n=0;n<t.length;++n){var i=t[n];e.push(this.read(i))}return this.geometryFactory.createGeometryCollection(e)}},Io={coordinate:function(t){return[t.x,t.y]},Point:function(t){var e=Io.coordinate.apply(this,[t.getCoordinate()]);return{type:"Point",coordinates:e}},MultiPoint:function(t){for(var e=[],n=0;n<t.geometries.length;++n){var i=t.geometries[n],r=Io.Point.apply(this,[i]);e.push(r.coordinates)}return{type:"MultiPoint",coordinates:e}},LineString:function(t){for(var e=[],n=t.getCoordinates(),i=0;i<n.length;++i){var r=n[i];e.push(Io.coordinate.apply(this,[r]))}return{type:"LineString",coordinates:e}},MultiLineString:function(t){for(var e=[],n=0;n<t.geometries.length;++n){var i=t.geometries[n],r=Io.LineString.apply(this,[i]);e.push(r.coordinates)}return{type:"MultiLineString",coordinates:e}},Polygon:function(t){var e=[],n=Io.LineString.apply(this,[t.shell]);e.push(n.coordinates);for(var i=0;i<t.holes.length;++i){var r=t.holes[i],s=Io.LineString.apply(this,[r]);e.push(s.coordinates)}return{type:"Polygon",coordinates:e}},MultiPolygon:function(t){for(var e=[],n=0;n<t.geometries.length;++n){var i=t.geometries[n],r=Io.Polygon.apply(this,[i]);e.push(r.coordinates)}return{type:"MultiPolygon",coordinates:e}},GeometryCollection:function(t){for(var e=[],n=0;n<t.geometries.length;++n){var i=t.geometries[n],r=i.getGeometryType();e.push(Io[r].apply(this,[i]))}return{type:"GeometryCollection",geometries:e}}};e(Vi.prototype,{read:function(t){var e=this.parser.read(t);return this.precisionModel.getType()===ee.FIXED&&this.reducePrecision(e),e},reducePrecision:function(t){var e,n;if(t.coordinate)this.precisionModel.makePrecise(t.coordinate);else if(t.points)for(e=0,n=t.points.length;n>e;e++)this.precisionModel.makePrecise(t.points[e]);else if(t.geometries)for(e=0,n=t.geometries.length;n>e;e++)this.reducePrecision(t.geometries[e])}}),e(ki.prototype,{write:function(t){return this.parser.write(t)}}),e(Yi.prototype,{read:function(t){var e=this.parser.read(t);return this.precisionModel.getType()===ee.FIXED&&this.reducePrecision(e),e},reducePrecision:function(t){if(t.coordinate)this.precisionModel.makePrecise(t.coordinate);else if(t.points)for(var e=0,n=t.points.coordinates.length;n>e;e++)this.precisionModel.makePrecise(t.points.coordinates[e]);else if(t.geometries)for(var i=0,r=t.geometries.length;r>i;i++)this.reducePrecision(t.geometries[i])}}),e(Xi.prototype,{read:function(t){return t instanceof ol.geom.Point?this.convertFromPoint(t):t instanceof ol.geom.LineString?this.convertFromLineString(t):t instanceof ol.geom.LinearRing?this.convertFromLinearRing(t):t instanceof ol.geom.Polygon?this.convertFromPolygon(t):t instanceof ol.geom.MultiPoint?this.convertFromMultiPoint(t):t instanceof ol.geom.MultiLineString?this.convertFromMultiLineString(t):t instanceof ol.geom.MultiPolygon?this.convertFromMultiPolygon(t):t instanceof ol.geom.GeometryCollection?this.convertFromCollection(t):void 0},convertFromPoint:function(t){var e=t.getCoordinates();return this.geometryFactory.createPoint(new g(e[0],e[1]))},convertFromLineString:function(t){return this.geometryFactory.createLineString(t.getCoordinates().map(function(t){return new g(t[0],t[1])}))},convertFromLinearRing:function(t){return this.geometryFactory.createLinearRing(t.getCoordinates().map(function(t){return new g(t[0],t[1])}))},convertFromPolygon:function(t){for(var e=t.getLinearRings(),n=null,i=[],r=0;r<e.length;r++){var s=this.convertFromLinearRing(e[r]);0===r?n=s:i.push(s)}return this.geometryFactory.createPolygon(n,i)},convertFromMultiPoint:function(t){var e=t.getPoints().map(function(t){return this.convertFromPoint(t)},this);return this.geometryFactory.createMultiPoint(e)},convertFromMultiLineString:function(t){var e=t.getLineStrings().map(function(t){return this.convertFromLineString(t)},this);return this.geometryFactory.createMultiLineString(e)},convertFromMultiPolygon:function(t){var e=t.getPolygons().map(function(t){return this.convertFromPolygon(t)},this);return this.geometryFactory.createMultiPolygon(e)},convertFromCollection:function(t){var e=t.getGeometries().map(function(t){return this.read(t)},this);return this.geometryFactory.createGeometryCollection(e)},write:function(t){return"Point"===t.getGeometryType()?this.convertToPoint(t.getCoordinate()):"LineString"===t.getGeometryType()?this.convertToLineString(t):"LinearRing"===t.getGeometryType()?this.convertToLinearRing(t):"Polygon"===t.getGeometryType()?this.convertToPolygon(t):"MultiPoint"===t.getGeometryType()?this.convertToMultiPoint(t):"MultiLineString"===t.getGeometryType()?this.convertToMultiLineString(t):"MultiPolygon"===t.getGeometryType()?this.convertToMultiPolygon(t):"GeometryCollection"===t.getGeometryType()?this.convertToCollection(t):void 0},convertToPoint:function(t){return new ol.geom.Point([t.x,t.y])},convertToLineString:function(t){var e=t.points.coordinates.map(Ui);return new ol.geom.LineString(e)},convertToLinearRing:function(t){var e=t.points.coordinates.map(Ui);return new ol.geom.LinearRing(e)},convertToPolygon:function(t){for(var e=[t.shell.points.coordinates.map(Ui)],n=0;n<t.holes.length;n++)e.push(t.holes[n].points.coordinates.map(Ui));return new ol.geom.Polygon(e)},convertToMultiPoint:function(t){return new ol.geom.MultiPoint(t.getCoordinates().map(Ui))},convertToMultiLineString:function(t){for(var e=[],n=0;n<t.geometries.length;n++)e.push(this.convertToLineString(t.geometries[n]).getCoordinates());return new ol.geom.MultiLineString(e)},convertToMultiPolygon:function(t){for(var e=[],n=0;n<t.geometries.length;n++)e.push(this.convertToPolygon(t.geometries[n]).getCoordinates());return new ol.geom.MultiPolygon(e)},convertToCollection:function(t){for(var e=[],n=0;n<t.geometries.length;n++){var i=t.geometries[n];e.push(this.write(i))}return new ol.geom.GeometryCollection(e)}});var No=Object.freeze({GeoJSONReader:Vi,GeoJSONWriter:ki,OL3Parser:Xi,WKTReader:Yi,WKTWriter:se});e(Hi.prototype,{rescale:function(){if(R(arguments[0],v))for(var t=arguments[0],e=t.iterator();e.hasNext();){var n=e.next();this.rescale(n.getCoordinates())}else if(arguments[0]instanceof Array){var i=arguments[0],r=null,s=null;2===i.length&&(r=new g(i[0]),s=new g(i[1]));for(var e=0;e<i.length;e++)i[e].x=i[e].x/this.scaleFactor+this.offsetX,i[e].y=i[e].y/this.scaleFactor+this.offsetY;2===i.length&&i[0].equals2D(i[1])&&A.out.println(i)}},scale:function(){if(R(arguments[0],v)){for(var t=arguments[0],e=new I,n=t.iterator();n.hasNext();){var i=n.next();e.add(new Ke(this.scale(i.getCoordinates()),i.getData()))}return e}if(arguments[0]instanceof Array){for(var r=arguments[0],s=new Array(r.length).fill(null),n=0;n<r.length;n++)s[n]=new g(Math.round((r[n].x-this.offsetX)*this.scaleFactor),Math.round((r[n].y-this.offsetY)*this.scaleFactor),r[n].z);var o=H.removeRepeatedPoints(s);return o}},isIntegerPrecision:function(){return 1===this.scaleFactor},getNodedSubstrings:function(){var t=this.noder.getNodedSubstrings();return this.isScaled&&this.rescale(t),t},computeNodes:function(t){var e=t;this.isScaled&&(e=this.scale(t)),this.noder.computeNodes(e)},interfaces_:function(){return[tn]},getClass:function(){return Hi}});var Co=Object.freeze({MCIndexNoder:nn,ScaledNoder:Hi,SegmentString:be});e(Wi.prototype,{isSimpleMultiPoint:function(t){if(t.isEmpty())return!0;for(var e=new at,n=0;n<t.getNumGeometries();n++){var i=t.getGeometryN(n),r=i.getCoordinate();if(e.contains(r))return this.nonSimpleLocation=r,!1;e.add(r)}return!0},isSimplePolygonal:function(t){for(var e=kn.getLines(t),n=e.iterator();n.hasNext();){var i=n.next();if(!this.isSimpleLinearGeometry(i))return!1}return!0},hasClosedEndpointIntersection:function(t){for(var e=new rt,n=t.getEdgeIterator();n.hasNext();){var i=n.next(),r=(i.getMaximumSegmentIndex(),i.isClosed()),s=i.getCoordinate(0);this.addEndpoint(e,s,r);var o=i.getCoordinate(i.getNumPoints()-1);this.addEndpoint(e,o,r)}for(var n=e.values().iterator();n.hasNext();){var a=n.next();if(a.isClosed&&2!==a.degree)return this.nonSimpleLocation=a.getCoordinate(),!0}return!1},getNonSimpleLocation:function(){return this.nonSimpleLocation},isSimpleLinearGeometry:function(t){if(t.isEmpty())return!0;var e=new $n(0,t),n=new ae,i=e.computeSelfNodes(n,!0);return i.hasIntersection()?i.hasProperIntersection()?(this.nonSimpleLocation=i.getProperIntersectionPoint(),!1):this.hasNonEndpointIntersection(e)?!1:!this.isClosedEndpointsInInterior||!this.hasClosedEndpointIntersection(e):!0},hasNonEndpointIntersection:function(t){for(var e=t.getEdgeIterator();e.hasNext();)for(var n=e.next(),i=n.getMaximumSegmentIndex(),r=n.getEdgeIntersectionList().iterator();r.hasNext();){var s=r.next();if(!s.isEndPoint(i))return this.nonSimpleLocation=s.getCoordinate(),!0}return!1},addEndpoint:function(t,e,n){var i=t.get(e);null===i&&(i=new ji(e),t.put(e,i)),i.addEndpoint(n)},computeSimple:function(t){return this.nonSimpleLocation=null,t.isEmpty()?!0:t instanceof St?this.isSimpleLinearGeometry(t):t instanceof gt?this.isSimpleLinearGeometry(t):t instanceof Pt?this.isSimpleMultiPoint(t):R(t,Rt)?this.isSimplePolygonal(t):t instanceof ft?this.isSimpleGeometryCollection(t):!0},isSimple:function(){return this.nonSimpleLocation=null,this.computeSimple(this.inputGeom)},isSimpleGeometryCollection:function(t){for(var e=0;e<t.getNumGeometries();e++){var n=t.getGeometryN(e);if(!this.computeSimple(n))return!1}return!0},interfaces_:function(){return[]},getClass:function(){return Wi}}),e(ji.prototype,{addEndpoint:function(t){this.degree++,this.isClosed|=t},getCoordinate:function(){return this.pt},interfaces_:function(){return[]},getClass:function(){return ji}}),Wi.EndpointInfo=ji,e(Ki.prototype,{getEndCapStyle:function(){return this.endCapStyle},isSingleSided:function(){return this._isSingleSided},setQuadrantSegments:function(t){this.quadrantSegments=t,0===this.quadrantSegments&&(this.joinStyle=Ki.JOIN_BEVEL),this.quadrantSegments<0&&(this.joinStyle=Ki.JOIN_MITRE,this.mitreLimit=Math.abs(this.quadrantSegments)),0>=t&&(this.quadrantSegments=1),this.joinStyle!==Ki.JOIN_ROUND&&(this.quadrantSegments=Ki.DEFAULT_QUADRANT_SEGMENTS)},getJoinStyle:function(){return this.joinStyle},setJoinStyle:function(t){this.joinStyle=t},setSimplifyFactor:function(t){this.simplifyFactor=0>t?0:t},getSimplifyFactor:function(){return this.simplifyFactor},getQuadrantSegments:function(){return this.quadrantSegments},setEndCapStyle:function(t){this.endCapStyle=t},getMitreLimit:function(){return this.mitreLimit},setMitreLimit:function(t){this.mitreLimit=t},setSingleSided:function(t){this._isSingleSided=t},interfaces_:function(){return[]},getClass:function(){return Ki}}),Ki.bufferDistanceError=function(t){var e=Math.PI/2/t;return 1-Math.cos(e/2)},Ki.CAP_ROUND=1,Ki.CAP_FLAT=2,Ki.CAP_SQUARE=3,Ki.JOIN_ROUND=1,Ki.JOIN_MITRE=2,Ki.JOIN_BEVEL=3,Ki.DEFAULT_QUADRANT_SEGMENTS=8,Ki.DEFAULT_MITRE_LIMIT=5,Ki.DEFAULT_SIMPLIFY_FACTOR=.01,e(Zi.prototype,{getCoordinate:function(){return this.minCoord},getRightmostSide:function(t,e){var n=this.getRightmostSideOfSegment(t,e);return 0>n&&(n=this.getRightmostSideOfSegment(t,e-1)),0>n&&(this.minCoord=null,this.checkForRightmostCoordinate(t)),n},findRightmostEdgeAtVertex:function(){var t=this.minDe.getEdge().getCoordinates();f.isTrue(this.minIndex>0&&this.minIndex<t.length,"rightmost point expected to be interior vertex of edge");var e=t[this.minIndex-1],n=t[this.minIndex+1],i=he.computeOrientation(this.minCoord,n,e),r=!1;e.y<this.minCoord.y&&n.y<this.minCoord.y&&i===he.COUNTERCLOCKWISE?r=!0:e.y>this.minCoord.y&&n.y>this.minCoord.y&&i===he.CLOCKWISE&&(r=!0),r&&(this.minIndex=this.minIndex-1)},getRightmostSideOfSegment:function(t,e){var n=t.getEdge(),i=n.getCoordinates();if(0>e||e+1>=i.length)return-1;if(i[e].y===i[e+1].y)return-1;var r=cn.LEFT;return i[e].y<i[e+1].y&&(r=cn.RIGHT),r},getEdge:function(){return this.orientedDe},checkForRightmostCoordinate:function(t){for(var e=t.getEdge().getCoordinates(),n=0;n<e.length-1;n++)(null===this.minCoord||e[n].x>this.minCoord.x)&&(this.minDe=t,this.minIndex=n,this.minCoord=e[n])},findRightmostEdgeAtNode:function(){var t=this.minDe.getNode(),e=t.getEdges();this.minDe=e.getRightmostEdge(),this.minDe.isForward()||(this.minDe=this.minDe.getSym(),this.minIndex=this.minDe.getEdge().getCoordinates().length-1)},findEdge:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();n.isForward()&&this.checkForRightmostCoordinate(n)}f.isTrue(0!==this.minIndex||this.minCoord.equals(this.minDe.getCoordinate()),"inconsistency in rightmost processing"),0===this.minIndex?this.findRightmostEdgeAtNode():this.findRightmostEdgeAtVertex(),this.orientedDe=this.minDe;var i=this.getRightmostSide(this.minDe,this.minIndex);i===cn.LEFT&&(this.orientedDe=this.minDe.getSym())},interfaces_:function(){return[]},getClass:function(){return Zi}}),Qi.prototype.addLast=function(t){this.array_.push(t)},Qi.prototype.removeFirst=function(){return this.array_.shift()},Qi.prototype.isEmpty=function(){return 0===this.array_.length},e(Ji.prototype,{clearVisitedEdges:function(){for(var t=this.dirEdgeList.iterator();t.hasNext();){var e=t.next();e.setVisited(!1)}},getRightmostCoordinate:function(){return this.rightMostCoord},computeNodeDepth:function(t){for(var e=null,n=t.getEdges().iterator();n.hasNext();){var i=n.next();if(i.isVisited()||i.getSym().isVisited()){e=i;break}}if(null===e)throw new sn("unable to find edge to compute depths at "+t.getCoordinate());t.getEdges().computeDepths(e);for(var n=t.getEdges().iterator();n.hasNext();){var i=n.next();i.setVisited(!0),this.copySymDepths(i)}},computeDepth:function(t){this.clearVisitedEdges();var e=this.finder.getEdge();e.getNode(),e.getLabel();e.setEdgeDepths(cn.RIGHT,t),this.copySymDepths(e),this.computeDepths(e)},create:function(t){this.addReachable(t),this.finder.findEdge(this.dirEdgeList),this.rightMostCoord=this.finder.getCoordinate()},findResultEdges:function(){for(var t=this.dirEdgeList.iterator();t.hasNext();){var e=t.next();e.getDepth(cn.RIGHT)>=1&&e.getDepth(cn.LEFT)<=0&&!e.isInteriorAreaEdge()&&e.setInResult(!0)}},computeDepths:function(t){var e=new J,n=new Qi,i=t.getNode();for(n.addLast(i),e.add(i),t.setVisited(!0);!n.isEmpty();){var r=n.removeFirst();e.add(r),this.computeNodeDepth(r);for(var s=r.getEdges().iterator();s.hasNext();){var o=s.next(),a=o.getSym();if(!a.isVisited()){var u=a.getNode();e.contains(u)||(n.addLast(u),e.add(u))}}}},compareTo:function(t){var e=t;return this.rightMostCoord.x<e.rightMostCoord.x?-1:this.rightMostCoord.x>e.rightMostCoord.x?1:0},getEnvelope:function(){if(null===this.env){for(var t=new C,e=this.dirEdgeList.iterator();e.hasNext();)for(var n=e.next(),i=n.getEdge().getCoordinates(),r=0;r<i.length-1;r++)t.expandToInclude(i[r]);this.env=t}return this.env},addReachable:function(t){var e=new pe;for(e.add(t);!e.empty();){var n=e.pop();this.add(n,e)}},copySymDepths:function(t){var e=t.getSym();e.setDepth(cn.LEFT,t.getDepth(cn.RIGHT)),e.setDepth(cn.RIGHT,t.getDepth(cn.LEFT))},add:function(t,e){t.setVisited(!0),this.nodes.add(t);for(var n=t.getEdges().iterator();n.hasNext();){var i=n.next();this.dirEdgeList.add(i);var r=i.getSym(),s=r.getNode();s.isVisited()||e.push(s)}},getNodes:function(){return this.nodes},getDirectedEdges:function(){return this.dirEdgeList},interfaces_:function(){return[s]},getClass:function(){return Ji}}),e($i.prototype,{isDeletable:function(t,e,n,i){var r=this.inputLine[t],s=this.inputLine[e],o=this.inputLine[n];return this.isConcave(r,s,o)&&this.isShallow(r,s,o,i)?this.isShallowSampled(r,s,t,n,i):!1},deleteShallowConcavities:function(){for(var t=1,e=(this.inputLine.length-1,this.findNextNonDeletedIndex(t)),n=this.findNextNonDeletedIndex(e),i=!1;n<this.inputLine.length;){var r=!1;this.isDeletable(t,e,n,this.distanceTol)&&(this.isDeleted[e]=$i.DELETE,r=!0,i=!0),t=r?n:e,e=this.findNextNonDeletedIndex(t),n=this.findNextNonDeletedIndex(e)}return i},isShallowConcavity:function(t,e,n,i){var r=he.computeOrientation(t,e,n),s=r===this.angleOrientation;if(!s)return!1;var o=he.distancePointLine(e,t,n);return i>o},isShallowSampled:function(t,e,n,i,r){var s=Math.trunc((i-n)/$i.NUM_PTS_TO_CHECK);0>=s&&(s=1);for(var o=n;i>o;o+=s)if(!this.isShallow(t,e,this.inputLine[o],r))return!1;return!0},isConcave:function Uo(t,e,n){var i=he.computeOrientation(t,e,n),Uo=i===this.angleOrientation;return Uo},simplify:function(t){this.distanceTol=Math.abs(t),0>t&&(this.angleOrientation=he.CLOCKWISE),this.isDeleted=new Array(this.inputLine.length).fill(null);var e=!1;do e=this.deleteShallowConcavities();while(e);return this.collapseLine()},findNextNonDeletedIndex:function(t){for(var e=t+1;e<this.inputLine.length&&this.isDeleted[e]===$i.DELETE;)e++;return e},isShallow:function(t,e,n,i){var r=he.distancePointLine(e,t,n);return i>r},collapseLine:function(){for(var t=new N,e=0;e<this.inputLine.length;e++)this.isDeleted[e]!==$i.DELETE&&t.add(this.inputLine[e]);return t.toCoordinateArray()},interfaces_:function(){return[]},getClass:function(){return $i}}),$i.simplify=function(t,e){var n=new $i(t);return n.simplify(e)},$i.INIT=0,$i.DELETE=1,$i.KEEP=1,$i.NUM_PTS_TO_CHECK=10,e(tr.prototype,{getCoordinates:function(){var t=this.ptList.toArray(tr.COORDINATE_ARRAY_TYPE);return t},setPrecisionModel:function(t){this.precisionModel=t},addPt:function(t){var e=new g(t);return this.precisionModel.makePrecise(e),this.isRedundant(e)?null:void this.ptList.add(e)},reverse:function(){},addPts:function(t,e){if(e)for(var n=0;n<t.length;n++)this.addPt(t[n]);else for(var n=t.length-1;n>=0;n--)this.addPt(t[n])},isRedundant:function(t){if(this.ptList.size()<1)return!1;var e=this.ptList.get(this.ptList.size()-1),n=t.distance(e);return n<this.minimimVertexDistance},toString:function(){var t=new ie,e=t.createLineString(this.getCoordinates());return e.toString()},closeRing:function(){if(this.ptList.size()<1)return null;var t=new g(this.ptList.get(0)),e=this.ptList.get(this.ptList.size()-1),n=null;return this.ptList.size()>=2&&(n=this.ptList.get(this.ptList.size()-2)),t.equals(e)?null:void this.ptList.add(t)},setMinimumVertexDistance:function(t){this.minimimVertexDistance=t},interfaces_:function(){return[]},getClass:function(){return tr}}),tr.COORDINATE_ARRAY_TYPE=new Array(0).fill(null),e(er.prototype,{addNextSegment:function(t,e){if(this.s0=this.s1,this.s1=this.s2,this.s2=t,this.seg0.setCoordinates(this.s0,this.s1),this.computeOffsetSegment(this.seg0,this.side,this.distance,this.offset0),this.seg1.setCoordinates(this.s1,this.s2),this.computeOffsetSegment(this.seg1,this.side,this.distance,this.offset1),this.s1.equals(this.s2))return null;var n=he.computeOrientation(this.s0,this.s1,this.s2),i=n===he.CLOCKWISE&&this.side===cn.LEFT||n===he.COUNTERCLOCKWISE&&this.side===cn.RIGHT;0===n?this.addCollinear(e):i?this.addOutsideTurn(n,e):this.addInsideTurn(n,e)},addLineEndCap:function(t,e){var n=new ce(t,e),i=new ce;this.computeOffsetSegment(n,cn.LEFT,this.distance,i);var r=new ce;this.computeOffsetSegment(n,cn.RIGHT,this.distance,r);var s=e.x-t.x,o=e.y-t.y,a=Math.atan2(o,s);switch(this.bufParams.getEndCapStyle()){case Ki.CAP_ROUND:this.segList.addPt(i.p1),this.addFilletArc(e,a+Math.PI/2,a-Math.PI/2,he.CLOCKWISE,this.distance),this.segList.addPt(r.p1);break;case Ki.CAP_FLAT:this.segList.addPt(i.p1),this.segList.addPt(r.p1);break;case Ki.CAP_SQUARE:var u=new g;u.x=Math.abs(this.distance)*Math.cos(a),u.y=Math.abs(this.distance)*Math.sin(a);var l=new g(i.p1.x+u.x,i.p1.y+u.y),h=new g(r.p1.x+u.x,r.p1.y+u.y);this.segList.addPt(l),this.segList.addPt(h)}},getCoordinates:function(){var t=this.segList.getCoordinates();return t},addMitreJoin:function(t,e,n,i){var r=!0,s=null;try{s=F.intersection(e.p0,e.p1,n.p0,n.p1);var o=0>=i?1:s.distance(t)/Math.abs(i);o>this.bufParams.getMitreLimit()&&(r=!1)}catch(a){if(!(a instanceof w))throw a;s=new g(0,0),r=!1}finally{}r?this.segList.addPt(s):this.addLimitedMitreJoin(e,n,i,this.bufParams.getMitreLimit())},addFilletCorner:function(t,e,n,i,r){var s=e.x-t.x,o=e.y-t.y,a=Math.atan2(o,s),u=n.x-t.x,l=n.y-t.y,h=Math.atan2(l,u);i===he.CLOCKWISE?h>=a&&(a+=2*Math.PI):a>=h&&(a-=2*Math.PI),this.segList.addPt(e),this.addFilletArc(t,a,h,i,r),this.segList.addPt(n)},addOutsideTurn:function(t,e){return this.offset0.p1.distance(this.offset1.p0)<this.distance*er.OFFSET_SEGMENT_SEPARATION_FACTOR?(this.segList.addPt(this.offset0.p1),null):void(this.bufParams.getJoinStyle()===Ki.JOIN_MITRE?this.addMitreJoin(this.s1,this.offset0,this.offset1,this.distance):this.bufParams.getJoinStyle()===Ki.JOIN_BEVEL?this.addBevelJoin(this.offset0,this.offset1):(e&&this.segList.addPt(this.offset0.p1),this.addFilletCorner(this.s1,this.offset0.p1,this.offset1.p0,t,this.distance),this.segList.addPt(this.offset1.p0)))},createSquare:function(t){this.segList.addPt(new g(t.x+this.distance,t.y+this.distance)),this.segList.addPt(new g(t.x+this.distance,t.y-this.distance)),this.segList.addPt(new g(t.x-this.distance,t.y-this.distance)),this.segList.addPt(new g(t.x-this.distance,t.y+this.distance)),this.segList.closeRing()},addSegments:function(t,e){this.segList.addPts(t,e)},addFirstSegment:function(){this.segList.addPt(this.offset1.p0)},addLastSegment:function(){this.segList.addPt(this.offset1.p1)},initSideSegments:function(t,e,n){this.s1=t,this.s2=e,this.side=n,this.seg1.setCoordinates(t,e),this.computeOffsetSegment(this.seg1,n,this.distance,this.offset1)},addLimitedMitreJoin:function(t,e,n,i){var r=this.seg0.p1,s=Ci.angle(r,this.seg0.p0),o=(Ci.angle(r,this.seg1.p1),Ci.angleBetweenOriented(this.seg0.p0,r,this.seg1.p1)),a=o/2,u=Ci.normalize(s+a),l=Ci.normalize(u+Math.PI),h=i*n,c=h*Math.abs(Math.sin(a)),f=n-c,d=r.x+h*Math.cos(l),p=r.y+h*Math.sin(l),v=new g(d,p),m=new ce(r,v),y=m.pointAlongOffset(1,f),x=m.pointAlongOffset(1,-f);
this.side===cn.LEFT?(this.segList.addPt(y),this.segList.addPt(x)):(this.segList.addPt(x),this.segList.addPt(y))},computeOffsetSegment:function(t,e,n,i){var r=e===cn.LEFT?1:-1,s=t.p1.x-t.p0.x,o=t.p1.y-t.p0.y,a=Math.sqrt(s*s+o*o),u=r*n*s/a,l=r*n*o/a;i.p0.x=t.p0.x-l,i.p0.y=t.p0.y+u,i.p1.x=t.p1.x-l,i.p1.y=t.p1.y+u},addFilletArc:function(t,e,n,i,r){var s=i===he.CLOCKWISE?-1:1,o=Math.abs(e-n),a=Math.trunc(o/this.filletAngleQuantum+.5);if(1>a)return null;var u=null,l=null;u=0,l=o/a;for(var h=u,c=new g;o>h;){var f=e+s*h;c.x=t.x+r*Math.cos(f),c.y=t.y+r*Math.sin(f),this.segList.addPt(c),h+=l}},addInsideTurn:function(t,e){if(this.li.computeIntersection(this.offset0.p0,this.offset0.p1,this.offset1.p0,this.offset1.p1),this.li.hasIntersection())this.segList.addPt(this.li.getIntersection(0));else if(this._hasNarrowConcaveAngle=!0,this.offset0.p1.distance(this.offset1.p0)<this.distance*er.INSIDE_TURN_VERTEX_SNAP_DISTANCE_FACTOR)this.segList.addPt(this.offset0.p1);else{if(this.segList.addPt(this.offset0.p1),this.closingSegLengthFactor>0){var n=new g((this.closingSegLengthFactor*this.offset0.p1.x+this.s1.x)/(this.closingSegLengthFactor+1),(this.closingSegLengthFactor*this.offset0.p1.y+this.s1.y)/(this.closingSegLengthFactor+1));this.segList.addPt(n);var i=new g((this.closingSegLengthFactor*this.offset1.p0.x+this.s1.x)/(this.closingSegLengthFactor+1),(this.closingSegLengthFactor*this.offset1.p0.y+this.s1.y)/(this.closingSegLengthFactor+1));this.segList.addPt(i)}else this.segList.addPt(this.s1);this.segList.addPt(this.offset1.p0)}},createCircle:function(t){var e=new g(t.x+this.distance,t.y);this.segList.addPt(e),this.addFilletArc(t,0,2*Math.PI,-1,this.distance),this.segList.closeRing()},addBevelJoin:function(t,e){this.segList.addPt(t.p1),this.segList.addPt(e.p0)},init:function(t){this.distance=t,this.maxCurveSegmentError=t*(1-Math.cos(this.filletAngleQuantum/2)),this.segList=new tr,this.segList.setPrecisionModel(this.precisionModel),this.segList.setMinimumVertexDistance(t*er.CURVE_VERTEX_SNAP_DISTANCE_FACTOR)},addCollinear:function(t){this.li.computeIntersection(this.s0,this.s1,this.s1,this.s2);var e=this.li.getIntersectionNum();e>=2&&(this.bufParams.getJoinStyle()===Ki.JOIN_BEVEL||this.bufParams.getJoinStyle()===Ki.JOIN_MITRE?(t&&this.segList.addPt(this.offset0.p1),this.segList.addPt(this.offset1.p0)):this.addFilletCorner(this.s1,this.offset0.p1,this.offset1.p0,he.CLOCKWISE,this.distance))},closeRing:function(){this.segList.closeRing()},hasNarrowConcaveAngle:function(){return this._hasNarrowConcaveAngle},interfaces_:function(){return[]},getClass:function(){return er}}),er.OFFSET_SEGMENT_SEPARATION_FACTOR=.001,er.INSIDE_TURN_VERTEX_SNAP_DISTANCE_FACTOR=.001,er.CURVE_VERTEX_SNAP_DISTANCE_FACTOR=1e-6,er.MAX_CLOSING_SEG_LEN_FACTOR=80,e(nr.prototype,{getOffsetCurve:function(t,e){if(this.distance=e,0===e)return null;var n=0>e,i=Math.abs(e),r=this.getSegGen(i);t.length<=1?this.computePointCurve(t[0],r):this.computeOffsetCurve(t,n,r);var s=r.getCoordinates();return n&&H.reverse(s),s},computeSingleSidedBufferCurve:function(t,e,n){var i=this.simplifyTolerance(this.distance);if(e){n.addSegments(t,!0);var r=$i.simplify(t,-i),s=r.length-1;n.initSideSegments(r[s],r[s-1],cn.LEFT),n.addFirstSegment();for(var o=s-2;o>=0;o--)n.addNextSegment(r[o],!0)}else{n.addSegments(t,!1);var a=$i.simplify(t,i),u=a.length-1;n.initSideSegments(a[0],a[1],cn.LEFT),n.addFirstSegment();for(var o=2;u>=o;o++)n.addNextSegment(a[o],!0)}n.addLastSegment(),n.closeRing()},computeRingBufferCurve:function(t,e,n){var i=this.simplifyTolerance(this.distance);e===cn.RIGHT&&(i=-i);var r=$i.simplify(t,i),s=r.length-1;n.initSideSegments(r[s-1],r[0],e);for(var o=1;s>=o;o++){var a=1!==o;n.addNextSegment(r[o],a)}n.closeRing()},computeLineBufferCurve:function(t,e){var n=this.simplifyTolerance(this.distance),i=$i.simplify(t,n),r=i.length-1;e.initSideSegments(i[0],i[1],cn.LEFT);for(var s=2;r>=s;s++)e.addNextSegment(i[s],!0);e.addLastSegment(),e.addLineEndCap(i[r-1],i[r]);var o=$i.simplify(t,-n),a=o.length-1;e.initSideSegments(o[a],o[a-1],cn.LEFT);for(var s=a-2;s>=0;s--)e.addNextSegment(o[s],!0);e.addLastSegment(),e.addLineEndCap(o[1],o[0]),e.closeRing()},computePointCurve:function(t,e){switch(this.bufParams.getEndCapStyle()){case Ki.CAP_ROUND:e.createCircle(t);break;case Ki.CAP_SQUARE:e.createSquare(t)}},getLineCurve:function(t,e){if(this.distance=e,0>e&&!this.bufParams.isSingleSided())return null;if(0===e)return null;var n=Math.abs(e),i=this.getSegGen(n);if(t.length<=1)this.computePointCurve(t[0],i);else if(this.bufParams.isSingleSided()){var r=0>e;this.computeSingleSidedBufferCurve(t,r,i)}else this.computeLineBufferCurve(t,i);var s=i.getCoordinates();return s},getBufferParameters:function(){return this.bufParams},simplifyTolerance:function(t){return t*this.bufParams.getSimplifyFactor()},getRingCurve:function(t,e,n){if(this.distance=n,t.length<=2)return this.getLineCurve(t,n);if(0===n)return nr.copyCoordinates(t);var i=this.getSegGen(n);return this.computeRingBufferCurve(t,e,i),i.getCoordinates()},computeOffsetCurve:function(t,e,n){var i=this.simplifyTolerance(this.distance);if(e){var r=$i.simplify(t,-i),s=r.length-1;n.initSideSegments(r[s],r[s-1],cn.LEFT),n.addFirstSegment();for(var o=s-2;o>=0;o--)n.addNextSegment(r[o],!0)}else{var a=$i.simplify(t,i),u=a.length-1;n.initSideSegments(a[0],a[1],cn.LEFT),n.addFirstSegment();for(var o=2;u>=o;o++)n.addNextSegment(a[o],!0)}n.addLastSegment()},getSegGen:function(t){return new er(this.precisionModel,this.bufParams,t)},interfaces_:function(){return[]},getClass:function(){return nr}}),nr.copyCoordinates=function(t){for(var e=new Array(t.length).fill(null),n=0;n<e.length;n++)e[n]=new g(t[n]);return e},e(ir.prototype,{findStabbedSegments:function(){if(1===arguments.length){for(var t=arguments[0],e=new I,n=this.subgraphs.iterator();n.hasNext();){var i=n.next(),r=i.getEnvelope();t.y<r.getMinY()||t.y>r.getMaxY()||this.findStabbedSegments(t,i.getDirectedEdges(),e)}return e}if(3===arguments.length)if(R(arguments[2],y)&&arguments[0]instanceof g&&arguments[1]instanceof In)for(var s=arguments[0],o=arguments[1],a=arguments[2],u=o.getEdge().getCoordinates(),n=0;n<u.length-1;n++){this.seg.p0=u[n],this.seg.p1=u[n+1],this.seg.p0.y>this.seg.p1.y&&this.seg.reverse();var l=Math.max(this.seg.p0.x,this.seg.p1.x);if(!(l<s.x||this.seg.isHorizontal()||s.y<this.seg.p0.y||s.y>this.seg.p1.y||he.computeOrientation(this.seg.p0,this.seg.p1,s)===he.RIGHT)){var h=o.getDepth(cn.LEFT);this.seg.p0.equals(u[n])||(h=o.getDepth(cn.RIGHT));var c=new rr(this.seg,h);a.add(c)}}else if(R(arguments[2],y)&&arguments[0]instanceof g&&R(arguments[1],y))for(var f=arguments[0],d=arguments[1],p=arguments[2],n=d.iterator();n.hasNext();){var v=n.next();v.isForward()&&this.findStabbedSegments(f,v,p)}},getDepth:function(t){var e=this.findStabbedSegments(t);if(0===e.size())return 0;var n=ho.min(e);return n.leftDepth},interfaces_:function(){return[]},getClass:function(){return ir}}),e(rr.prototype,{compareTo:function(t){var e=t;if(this.upwardSeg.minX()>=e.upwardSeg.maxX())return 1;if(this.upwardSeg.maxX()<=e.upwardSeg.minX())return-1;var n=this.upwardSeg.orientationIndex(e.upwardSeg);return 0!==n?n:(n=-1*e.upwardSeg.orientationIndex(this.upwardSeg),0!==n?n:this.upwardSeg.compareTo(e.upwardSeg))},compareX:function(t,e){var n=t.p0.compareTo(e.p0);return 0!==n?n:t.p1.compareTo(e.p1)},toString:function(){return this.upwardSeg.toString()},interfaces_:function(){return[s]},getClass:function(){return rr}}),ir.DepthSegment=rr,e(sr.prototype,{addPoint:function(t){if(this.distance<=0)return null;var e=t.getCoordinates(),n=this.curveBuilder.getLineCurve(e,this.distance);this.addCurve(n,L.EXTERIOR,L.INTERIOR)},addPolygon:function(t){var e=this.distance,n=cn.LEFT;this.distance<0&&(e=-this.distance,n=cn.RIGHT);var i=t.getExteriorRing(),r=H.removeRepeatedPoints(i.getCoordinates());if(this.distance<0&&this.isErodedCompletely(i,this.distance))return null;if(this.distance<=0&&r.length<3)return null;this.addPolygonRing(r,e,n,L.EXTERIOR,L.INTERIOR);for(var s=0;s<t.getNumInteriorRing();s++){var o=t.getInteriorRingN(s),a=H.removeRepeatedPoints(o.getCoordinates());this.distance>0&&this.isErodedCompletely(o,-this.distance)||this.addPolygonRing(a,e,cn.opposite(n),L.INTERIOR,L.EXTERIOR)}},isTriangleErodedCompletely:function(t,e){var n=new Si(t[0],t[1],t[2]),i=n.inCentre(),r=he.distancePointLine(i,n.p0,n.p1);return r<Math.abs(e)},addLineString:function(t){if(this.distance<=0&&!this.curveBuilder.getBufferParameters().isSingleSided())return null;var e=H.removeRepeatedPoints(t.getCoordinates()),n=this.curveBuilder.getLineCurve(e,this.distance);this.addCurve(n,L.EXTERIOR,L.INTERIOR)},addCurve:function(t,e,n){if(null===t||t.length<2)return null;var i=new Ke(t,new gn(0,L.BOUNDARY,e,n));this.curveList.add(i)},getCurves:function(){return this.add(this.inputGeom),this.curveList},addPolygonRing:function(t,e,n,i,r){if(0===e&&t.length<bt.MINIMUM_VALID_SIZE)return null;var s=i,o=r;t.length>=bt.MINIMUM_VALID_SIZE&&he.isCCW(t)&&(s=r,o=i,n=cn.opposite(n));var a=this.curveBuilder.getRingCurve(t,n,e);this.addCurve(a,s,o)},add:function(t){if(t.isEmpty())return null;if(t instanceof Tt)this.addPolygon(t);else if(t instanceof St)this.addLineString(t);else if(t instanceof Lt)this.addPoint(t);else if(t instanceof Pt)this.addCollection(t);else if(t instanceof gt)this.addCollection(t);else if(t instanceof Ot)this.addCollection(t);else{if(!(t instanceof ft))throw new UnsupportedOperationException(t.getClass().getName());this.addCollection(t)}},isErodedCompletely:function(t,e){var n=t.getCoordinates();if(n.length<4)return 0>e;if(4===n.length)return this.isTriangleErodedCompletely(n,e);var i=t.getEnvelopeInternal(),r=Math.min(i.getHeight(),i.getWidth());return 0>e&&2*Math.abs(e)>r},addCollection:function(t){for(var e=0;e<t.getNumGeometries();e++){var n=t.getGeometryN(e);this.add(n)}},interfaces_:function(){return[]},getClass:function(){return sr}}),e(or.prototype,{isTrivialIntersection:function(t,e,n,i){if(t===n&&1===this.li.getIntersectionNum()){if(or.isAdjacentSegments(e,i))return!0;if(t.isClosed()){var r=t.size()-1;if(0===e&&i===r||0===i&&e===r)return!0}}return!1},getProperIntersectionPoint:function(){return this.properIntersectionPoint},hasProperInteriorIntersection:function(){return this.hasProperInterior},getLineIntersector:function(){return this.li},hasProperIntersection:function(){return this.hasProper},processIntersections:function(t,e,n,i){if(t===n&&e===i)return null;this.numTests++;var r=t.getCoordinates()[e],s=t.getCoordinates()[e+1],o=n.getCoordinates()[i],a=n.getCoordinates()[i+1];this.li.computeIntersection(r,s,o,a),this.li.hasIntersection()&&(this.numIntersections++,this.li.isInteriorIntersection()&&(this.numInteriorIntersections++,this.hasInterior=!0),this.isTrivialIntersection(t,e,n,i)||(this._hasIntersection=!0,t.addIntersections(this.li,e,0),n.addIntersections(this.li,i,1),this.li.isProper()&&(this.numProperIntersections++,this.hasProper=!0,this.hasProperInterior=!0)))},hasIntersection:function(){return this._hasIntersection},isDone:function(){return!1},hasInteriorIntersection:function(){return this.hasInterior},interfaces_:function(){return[on]},getClass:function(){return or}}),or.isAdjacentSegments=function(t,e){return 1===Math.abs(t-e)},e(ar.prototype,{setWorkingPrecisionModel:function(t){this.workingPrecisionModel=t},insertUniqueEdge:function(t){var e=this.edgeList.findEqualEdge(t);if(null!==e){var n=e.getLabel(),i=t.getLabel();e.isPointwiseEqual(t)||(i=new gn(t.getLabel()),i.flip()),n.merge(i);var r=ar.depthDelta(i),s=e.getDepthDelta(),o=s+r;e.setDepthDelta(o)}else this.edgeList.add(t),t.setDepthDelta(ar.depthDelta(t.getLabel()))},buildSubgraphs:function(t,e){for(var n=new I,i=t.iterator();i.hasNext();){var r=i.next(),s=r.getRightmostCoordinate(),o=new ir(n),a=o.getDepth(s);r.computeDepth(a),r.findResultEdges(),n.add(r),e.add(r.getDirectedEdges(),r.getNodes())}},createSubgraphs:function(t){for(var e=new I,n=t.getNodes().iterator();n.hasNext();){var i=n.next();if(!i.isVisited()){var r=new Ji;r.create(i),e.add(r)}}return ho.sort(e,ho.reverseOrder()),e},createEmptyResultGeometry:function(){var t=this.geomFact.createPolygon();return t},getNoder:function(t){if(null!==this.workingNoder)return this.workingNoder;var e=new nn,n=new ae;return n.setPrecisionModel(t),e.setSegmentIntersector(new or(n)),e},buffer:function(t,e){var n=this.workingPrecisionModel;null===n&&(n=t.getPrecisionModel()),this.geomFact=t.getFactory();var i=new nr(n,this.bufParams),r=new sr(t,e,i),s=r.getCurves();if(s.size()<=0)return this.createEmptyResultGeometry();this.computeNodedEdges(s,n),this.graph=new Cn(new On),this.graph.addEdges(this.edgeList.getEdges());var o=this.createSubgraphs(this.graph),a=new Sn(this.geomFact);this.buildSubgraphs(o,a);var u=a.getPolygons();if(u.size()<=0)return this.createEmptyResultGeometry();var l=this.geomFact.buildGeometry(u);return l},computeNodedEdges:function(t,e){var n=this.getNoder(e);n.computeNodes(t);for(var i=n.getNodedSubstrings(),r=i.iterator();r.hasNext();){var s=r.next(),o=s.getCoordinates();if(2!==o.length||!o[0].equals2D(o[1])){var a=s.getData(),u=new Jn(s.getCoordinates(),new gn(a));this.insertUniqueEdge(u)}}},setNoder:function(t){this.workingNoder=t},interfaces_:function(){return[]},getClass:function(){return ar}}),ar.depthDelta=function(t){var e=t.getLocation(0,cn.LEFT),n=t.getLocation(0,cn.RIGHT);return e===L.INTERIOR&&n===L.EXTERIOR?1:e===L.EXTERIOR&&n===L.INTERIOR?-1:0},ar.convertSegStrings=function(t){for(var e=new ie,n=new I;t.hasNext();){var i=t.next(),r=e.createLineString(i.getCoordinates());n.add(r)}return e.buildGeometry(n)},e(ur.prototype,{checkEndPtVertexIntersections:function(){if(0===arguments.length)for(var t=this.segStrings.iterator();t.hasNext();){var e=t.next(),n=e.getCoordinates();this.checkEndPtVertexIntersections(n[0],this.segStrings),this.checkEndPtVertexIntersections(n[n.length-1],this.segStrings)}else if(2===arguments.length)for(var i=arguments[0],r=arguments[1],t=r.iterator();t.hasNext();)for(var e=t.next(),n=e.getCoordinates(),s=1;s<n.length-1;s++)if(n[s].equals(i))throw new l("found endpt/interior pt intersection at index "+s+" :pt "+i)},checkInteriorIntersections:function(){if(0===arguments.length)for(var t=this.segStrings.iterator();t.hasNext();)for(var e=t.next(),n=this.segStrings.iterator();n.hasNext();){var i=n.next();this.checkInteriorIntersections(e,i)}else if(2===arguments.length)for(var r=arguments[0],s=arguments[1],o=r.getCoordinates(),a=s.getCoordinates(),u=0;u<o.length-1;u++)for(var h=0;h<a.length-1;h++)this.checkInteriorIntersections(r,u,s,h);else if(4===arguments.length){var c=arguments[0],f=arguments[1],g=arguments[2],d=arguments[3];if(c===g&&f===d)return null;var p=c.getCoordinates()[f],v=c.getCoordinates()[f+1],m=g.getCoordinates()[d],y=g.getCoordinates()[d+1];if(this.li.computeIntersection(p,v,m,y),this.li.hasIntersection()&&(this.li.isProper()||this.hasInteriorIntersection(this.li,p,v)||this.hasInteriorIntersection(this.li,m,y)))throw new l("found non-noded intersection at "+p+"-"+v+" and "+m+"-"+y)}},checkValid:function(){this.checkEndPtVertexIntersections(),this.checkInteriorIntersections(),this.checkCollapses()},checkCollapses:function(){if(0===arguments.length)for(var t=this.segStrings.iterator();t.hasNext();){var e=t.next();this.checkCollapses(e)}else if(1===arguments.length)for(var n=arguments[0],i=n.getCoordinates(),t=0;t<i.length-2;t++)this.checkCollapse(i[t],i[t+1],i[t+2])},hasInteriorIntersection:function(t,e,n){for(var i=0;i<t.getIntersectionNum();i++){var r=t.getIntersection(i);if(!r.equals(e)&&!r.equals(n))return!0}return!1},checkCollapse:function(t,e,n){if(t.equals(n))throw new l("found non-noded collapse at "+ur.fact.createLineString([t,e,n]))},interfaces_:function(){return[]},getClass:function(){return ur}}),ur.fact=new ie,e(lr.prototype,{intersectsScaled:function(t,e){var n=Math.min(t.x,e.x),i=Math.max(t.x,e.x),r=Math.min(t.y,e.y),s=Math.max(t.y,e.y),o=this.maxx<n||this.minx>i||this.maxy<r||this.miny>s;if(o)return!1;var a=this.intersectsToleranceSquare(t,e);return f.isTrue(!(o&&a),"Found bad envelope test"),a},initCorners:function(t){var e=.5;this.minx=t.x-e,this.maxx=t.x+e,this.miny=t.y-e,this.maxy=t.y+e,this.corner[0]=new g(this.maxx,this.maxy),this.corner[1]=new g(this.minx,this.maxy),this.corner[2]=new g(this.minx,this.miny),this.corner[3]=new g(this.maxx,this.miny)},intersects:function(t,e){return 1===this.scaleFactor?this.intersectsScaled(t,e):(this.copyScaled(t,this.p0Scaled),this.copyScaled(e,this.p1Scaled),this.intersectsScaled(this.p0Scaled,this.p1Scaled))},scale:function(t){return Math.round(t*this.scaleFactor)},getCoordinate:function(){return this.originalPt},copyScaled:function(t,e){e.x=this.scale(t.x),e.y=this.scale(t.y)},getSafeEnvelope:function(){if(null===this.safeEnv){var t=lr.SAFE_ENV_EXPANSION_FACTOR/this.scaleFactor;this.safeEnv=new C(this.originalPt.x-t,this.originalPt.x+t,this.originalPt.y-t,this.originalPt.y+t)}return this.safeEnv},intersectsPixelClosure:function(t,e){return this.li.computeIntersection(t,e,this.corner[0],this.corner[1]),this.li.hasIntersection()?!0:(this.li.computeIntersection(t,e,this.corner[1],this.corner[2]),this.li.hasIntersection()?!0:(this.li.computeIntersection(t,e,this.corner[2],this.corner[3]),this.li.hasIntersection()?!0:(this.li.computeIntersection(t,e,this.corner[3],this.corner[0]),!!this.li.hasIntersection())))},intersectsToleranceSquare:function(t,e){var n=!1,i=!1;return this.li.computeIntersection(t,e,this.corner[0],this.corner[1]),this.li.isProper()?!0:(this.li.computeIntersection(t,e,this.corner[1],this.corner[2]),this.li.isProper()?!0:(this.li.hasIntersection()&&(n=!0),this.li.computeIntersection(t,e,this.corner[2],this.corner[3]),this.li.isProper()?!0:(this.li.hasIntersection()&&(i=!0),this.li.computeIntersection(t,e,this.corner[3],this.corner[0]),this.li.isProper()?!0:n&&i?!0:t.equals(this.pt)?!0:!!e.equals(this.pt))))},addSnappedNode:function(t,e){var n=t.getCoordinate(e),i=t.getCoordinate(e+1);return this.intersects(n,i)?(t.addIntersection(this.getCoordinate(),e),!0):!1},interfaces_:function(){return[]},getClass:function(){return lr}}),lr.SAFE_ENV_EXPANSION_FACTOR=.75,e(hr.prototype,{snap:function(){if(1===arguments.length){var t=arguments[0];return this.snap(t,null,-1)}if(3===arguments.length){var e=arguments[0],n=arguments[1],i=arguments[2],r=e.getSafeEnvelope(),s=new cr(e,n,i);return this.index.query(r,{interfaces_:function(){return[Ae]},visitItem:function(t){var e=t;e.select(r,s)}}),s.isNodeAdded()}},interfaces_:function(){return[]},getClass:function(){return hr}}),h(cr,hi),e(cr.prototype,{isNodeAdded:function(){return this._isNodeAdded},select:function(){if(2!==arguments.length)return hi.prototype.select.apply(this,arguments);var t=arguments[0],e=arguments[1],n=t.getContext();return null!==this.parentEdge&&n===this.parentEdge&&e===this.hotPixelVertexIndex?null:void(this._isNodeAdded=this.hotPixel.addSnappedNode(n,e))},interfaces_:function(){return[]},getClass:function(){return cr}}),hr.HotPixelSnapAction=cr,e(fr.prototype,{processIntersections:function(t,e,n,i){if(t===n&&e===i)return null;var r=t.getCoordinates()[e],s=t.getCoordinates()[e+1],o=n.getCoordinates()[i],a=n.getCoordinates()[i+1];if(this.li.computeIntersection(r,s,o,a),this.li.hasIntersection()&&this.li.isInteriorIntersection()){for(var u=0;u<this.li.getIntersectionNum();u++)this.interiorIntersections.add(this.li.getIntersection(u));t.addIntersections(this.li,e,0),n.addIntersections(this.li,i,1)}},isDone:function(){return!1},getInteriorIntersections:function(){return this.interiorIntersections},interfaces_:function(){return[on]},getClass:function(){return fr}}),e(gr.prototype,{checkCorrectness:function(t){var e=Ke.getNodedSubstrings(t),n=new ur(e);try{n.checkValid()}catch(i){if(!(i instanceof S))throw i;i.printStackTrace()}finally{}},getNodedSubstrings:function(){return Ke.getNodedSubstrings(this.nodedSegStrings)},snapRound:function(t,e){var n=this.findInteriorIntersections(t,e);this.computeIntersectionSnaps(n),this.computeVertexSnaps(t)},findInteriorIntersections:function(t,e){var n=new fr(e);return this.noder.setSegmentIntersector(n),this.noder.computeNodes(t),n.getInteriorIntersections()},computeVertexSnaps:function(){if(R(arguments[0],v))for(var t=arguments[0],e=t.iterator();e.hasNext();){var n=e.next();this.computeVertexSnaps(n)}else if(arguments[0]instanceof Ke)for(var i=arguments[0],r=i.getCoordinates(),s=0;s<r.length;s++){var o=new lr(r[s],this.scaleFactor,this.li),a=this.pointSnapper.snap(o,i,s);a&&i.addIntersection(r[s],s)}},computeNodes:function(t){this.nodedSegStrings=t,this.noder=new nn,this.pointSnapper=new hr(this.noder.getIndex()),this.snapRound(t,this.li)},computeIntersectionSnaps:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next(),i=new lr(n,this.scaleFactor,this.li);this.pointSnapper.snap(i)}},interfaces_:function(){return[tn]},getClass:function(){return gr}}),e(dr.prototype,{bufferFixedPrecision:function(t){var e=new Hi(new gr(new ee(1)),t.getScale()),n=new ar(this.bufParams);n.setWorkingPrecisionModel(t),n.setNoder(e),this.resultGeometry=n.buffer(this.argGeom,this.distance)},bufferReducedPrecision:function(){if(0===arguments.length){for(var t=dr.MAX_PRECISION_DIGITS;t>=0;t--){try{this.bufferReducedPrecision(t)}catch(e){if(!(e instanceof sn))throw e;this.saveException=e}finally{}if(null!==this.resultGeometry)return null}throw this.saveException}if(1===arguments.length){var n=arguments[0],i=dr.precisionScaleFactor(this.argGeom,this.distance,n),r=new ee(i);this.bufferFixedPrecision(r)}},computeGeometry:function(){if(this.bufferOriginalPrecision(),null!==this.resultGeometry)return null;var t=this.argGeom.getFactory().getPrecisionModel();t.getType()===ee.FIXED?this.bufferFixedPrecision(t):this.bufferReducedPrecision()},setQuadrantSegments:function(t){this.bufParams.setQuadrantSegments(t)},bufferOriginalPrecision:function(){try{var t=new ar(this.bufParams);this.resultGeometry=t.buffer(this.argGeom,this.distance)}catch(e){if(!(e instanceof l))throw e;this.saveException=e}finally{}},getResultGeometry:function(t){return this.distance=t,this.computeGeometry(),this.resultGeometry},setEndCapStyle:function(t){this.bufParams.setEndCapStyle(t)},interfaces_:function(){return[]},getClass:function(){return dr}}),dr.bufferOp=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=new dr(t),i=n.getResultGeometry(e);return i}if(3===arguments.length){if(Number.isInteger(arguments[2])&&arguments[0]instanceof B&&"number"==typeof arguments[1]){var r=arguments[0],s=arguments[1],o=arguments[2],a=new dr(r);a.setQuadrantSegments(o);var i=a.getResultGeometry(s);return i}if(arguments[2]instanceof Ki&&arguments[0]instanceof B&&"number"==typeof arguments[1]){var u=arguments[0],l=arguments[1],h=arguments[2],a=new dr(u,h),i=a.getResultGeometry(l);return i}}else if(4===arguments.length){var c=arguments[0],f=arguments[1],g=arguments[2],d=arguments[3],a=new dr(c);a.setQuadrantSegments(g),a.setEndCapStyle(d);var i=a.getResultGeometry(f);return i}},dr.precisionScaleFactor=function(t,e,n){var i=t.getEnvelopeInternal(),r=T.max(Math.abs(i.getMaxX()),Math.abs(i.getMaxY()),Math.abs(i.getMinX()),Math.abs(i.getMinY())),s=e>0?e:0,o=r+2*s,a=Math.trunc(Math.log(o)/Math.log(10)+1),u=n-a,l=Math.pow(10,u);return l},dr.CAP_ROUND=Ki.CAP_ROUND,dr.CAP_BUTT=Ki.CAP_FLAT,dr.CAP_FLAT=Ki.CAP_FLAT,dr.CAP_SQUARE=Ki.CAP_SQUARE,dr.MAX_PRECISION_DIGITS=12;var So=Object.freeze({BufferOp:dr,BufferParameters:Ki});e(pr.prototype,{filter:function(t){t instanceof Tt&&this.comps.add(t)},interfaces_:function(){return[ht]},getClass:function(){return pr}}),pr.getPolygons=function(){if(1===arguments.length){var t=arguments[0];return pr.getPolygons(t,new I)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return e instanceof Tt?n.add(e):e instanceof ft&&e.apply(new pr(n)),n}},e(vr.prototype,{isInsideArea:function(){return this.segIndex===vr.INSIDE_AREA},getCoordinate:function(){return this.pt},getGeometryComponent:function(){return this.component},getSegmentIndex:function(){return this.segIndex},interfaces_:function(){return[]},getClass:function(){return vr}}),vr.INSIDE_AREA=-1,e(mr.prototype,{filter:function(t){t instanceof Lt&&this.pts.add(t)},interfaces_:function(){return[ht]},getClass:function(){return mr}}),mr.getPoints=function(){if(1===arguments.length){var t=arguments[0];return t instanceof Lt?ho.singletonList(t):mr.getPoints(t,new I)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return e instanceof Lt?n.add(e):e instanceof ft&&e.apply(new mr(n)),n}},e(yr.prototype,{filter:function(t){(t instanceof Lt||t instanceof St||t instanceof Tt)&&this.locations.add(new vr(t,0,t.getCoordinate()))},interfaces_:function(){return[ht]},getClass:function(){return yr}}),yr.getLocations=function(t){var e=new I;return t.apply(new yr(e)),e},e(xr.prototype,{computeContainmentDistance:function(){if(0===arguments.length){var t=new Array(2).fill(null);if(this.computeContainmentDistance(0,t),this.minDistance<=this.terminateDistance)return null;this.computeContainmentDistance(1,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1],i=1-e,r=pr.getPolygons(this.geom[e]);if(r.size()>0){var s=yr.getLocations(this.geom[i]);if(this.computeContainmentDistance(s,r,n),this.minDistance<=this.terminateDistance)return this.minDistanceLocation[i]=n[0],this.minDistanceLocation[e]=n[1],null}}else if(3===arguments.length)if(arguments[2]instanceof Array&&R(arguments[0],y)&&R(arguments[1],y)){for(var o=arguments[0],a=arguments[1],u=arguments[2],l=0;l<o.size();l++)for(var h=o.get(l),c=0;c<a.size();c++)if(this.computeContainmentDistance(h,a.get(c),u),this.minDistance<=this.terminateDistance)return null}else if(arguments[2]instanceof Array&&arguments[0]instanceof vr&&arguments[1]instanceof Tt){var f=arguments[0],g=arguments[1],d=arguments[2],p=f.getCoordinate();if(L.EXTERIOR!==this.ptLocator.locate(p,g))return this.minDistance=0,d[0]=f,d[1]=new vr(g,p),null}},computeMinDistanceLinesPoints:function(t,e,n){for(var i=0;i<t.size();i++)for(var r=t.get(i),s=0;s<e.size();s++){var o=e.get(s);if(this.computeMinDistance(r,o,n),this.minDistance<=this.terminateDistance)return null}},computeFacetDistance:function(){var t=new Array(2).fill(null),e=kn.getLines(this.geom[0]),n=kn.getLines(this.geom[1]),i=mr.getPoints(this.geom[0]),r=mr.getPoints(this.geom[1]);return this.computeMinDistanceLines(e,n,t),this.updateMinDistance(t,!1),this.minDistance<=this.terminateDistance?null:(t[0]=null,t[1]=null,this.computeMinDistanceLinesPoints(e,r,t),this.updateMinDistance(t,!1),this.minDistance<=this.terminateDistance?null:(t[0]=null,t[1]=null,this.computeMinDistanceLinesPoints(n,i,t),this.updateMinDistance(t,!0),this.minDistance<=this.terminateDistance?null:(t[0]=null,t[1]=null,this.computeMinDistancePoints(i,r,t),void this.updateMinDistance(t,!1))))},nearestLocations:function(){return this.computeMinDistance(),this.minDistanceLocation},updateMinDistance:function(t,e){return null===t[0]?null:void(e?(this.minDistanceLocation[0]=t[1],this.minDistanceLocation[1]=t[0]):(this.minDistanceLocation[0]=t[0],this.minDistanceLocation[1]=t[1]))},nearestPoints:function(){this.computeMinDistance();var t=[this.minDistanceLocation[0].getCoordinate(),this.minDistanceLocation[1].getCoordinate()];return t},computeMinDistance:function(){if(0===arguments.length){if(null!==this.minDistanceLocation)return null;if(this.minDistanceLocation=new Array(2).fill(null),this.computeContainmentDistance(),this.minDistance<=this.terminateDistance)return null;this.computeFacetDistance()}else if(3===arguments.length)if(arguments[2]instanceof Array&&arguments[0]instanceof St&&arguments[1]instanceof Lt){var t=arguments[0],e=arguments[1],n=arguments[2];if(t.getEnvelopeInternal().distance(e.getEnvelopeInternal())>this.minDistance)return null;for(var i=t.getCoordinates(),r=e.getCoordinate(),s=0;s<i.length-1;s++){var o=he.distancePointLine(r,i[s],i[s+1]);if(o<this.minDistance){this.minDistance=o;var a=new ce(i[s],i[s+1]),u=a.closestPoint(r);n[0]=new vr(t,s,u),n[1]=new vr(e,0,r)}if(this.minDistance<=this.terminateDistance)return null}}else if(arguments[2]instanceof Array&&arguments[0]instanceof St&&arguments[1]instanceof St){var l=arguments[0],h=arguments[1],c=arguments[2];if(l.getEnvelopeInternal().distance(h.getEnvelopeInternal())>this.minDistance)return null;for(var i=l.getCoordinates(),f=h.getCoordinates(),s=0;s<i.length-1;s++)for(var g=0;g<f.length-1;g++){var o=he.distanceLineLine(i[s],i[s+1],f[g],f[g+1]);if(o<this.minDistance){this.minDistance=o;var d=new ce(i[s],i[s+1]),p=new ce(f[g],f[g+1]),v=d.closestPoints(p);c[0]=new vr(l,s,v[0]),c[1]=new vr(h,g,v[1])}if(this.minDistance<=this.terminateDistance)return null}}},computeMinDistancePoints:function(t,e,n){for(var i=0;i<t.size();i++)for(var r=t.get(i),s=0;s<e.size();s++){var o=e.get(s),a=r.getCoordinate().distance(o.getCoordinate());if(a<this.minDistance&&(this.minDistance=a,n[0]=new vr(r,0,r.getCoordinate()),n[1]=new vr(o,0,o.getCoordinate())),this.minDistance<=this.terminateDistance)return null}},distance:function(){if(null===this.geom[0]||null===this.geom[1])throw new i("null geometries are not supported");return this.geom[0].isEmpty()||this.geom[1].isEmpty()?0:(this.computeMinDistance(),this.minDistance)},computeMinDistanceLines:function(t,e,n){for(var i=0;i<t.size();i++)for(var r=t.get(i),s=0;s<e.size();s++){var o=e.get(s);if(this.computeMinDistance(r,o,n),this.minDistance<=this.terminateDistance)return null}},interfaces_:function(){return[]},getClass:function(){return xr}}),xr.distance=function(t,e){var n=new xr(t,e);return n.distance()},xr.isWithinDistance=function(t,e,n){var i=new xr(t,e,n);return i.distance()<=n},xr.nearestPoints=function(t,e){var n=new xr(t,e);return n.nearestPoints()};var wo=Object.freeze({DistanceOp:xr});e(Er.prototype,{getCoordinates:function(){if(null===this.coordinates){for(var t=0,e=0,n=new N,i=this.directedEdges.iterator();i.hasNext();){var r=i.next();r.getEdgeDirection()?t++:e++,n.add(r.getEdge().getLine().getCoordinates(),!1,r.getEdgeDirection())}this.coordinates=n.toCoordinateArray(),e>t&&H.reverse(this.coordinates)}return this.coordinates},toLineString:function(){return this.factory.createLineString(this.getCoordinates())},add:function(t){this.directedEdges.add(t)},interfaces_:function(){return[]},getClass:function(){return Er}}),e(Ir.prototype,{setVisited:function(t){this._isVisited=t},isMarked:function(){return this._isMarked},setData:function(t){this.data=t},getData:function(){return this.data},setMarked:function(t){this._isMarked=t},getContext:function(){return this.data},isVisited:function(){return this._isVisited},setContext:function(t){this.data=t},interfaces_:function(){return[]},getClass:function(){return Ir}}),Ir.getComponentWithVisitedState=function(t,e){for(;t.hasNext();){var n=t.next();if(n.isVisited()===e)return n}return null},Ir.setVisited=function(t,e){for(;t.hasNext();){var n=t.next();n.setVisited(e)}},Ir.setMarked=function(t,e){for(;t.hasNext();){var n=t.next();n.setMarked(e)}},h(Nr,Ir),e(Nr.prototype,{isRemoved:function(){return null===this.parentEdge},compareDirection:function(t){return this.quadrant>t.quadrant?1:this.quadrant<t.quadrant?-1:he.computeOrientation(t.p0,t.p1,this.p1)},getCoordinate:function(){return this.from.getCoordinate()},print:function(t){var e=this.getClass().getName(),n=e.lastIndexOf("."),i=e.substring(n+1);t.print("  "+i+": "+this.p0+" - "+this.p1+" "+this.quadrant+":"+this.angle)},getDirectionPt:function(){return this.p1},getAngle:function(){return this.angle},compareTo:function(t){var e=t;return this.compareDirection(e)},getFromNode:function(){return this.from},getSym:function(){return this.sym},setEdge:function(t){this.parentEdge=t},remove:function(){this.sym=null,this.parentEdge=null},getEdge:function(){return this.parentEdge},getQuadrant:function(){return this.quadrant},setSym:function(t){this.sym=t},getToNode:function(){return this.to},getEdgeDirection:function(){return this.edgeDirection},interfaces_:function(){return[s]},getClass:function(){return Nr}}),Nr.toEdges=function(t){
for(var e=new I,n=t.iterator();n.hasNext();)e.add(n.next().parentEdge);return e},h(Cr,Nr),e(Cr.prototype,{getNext:function(){return 2!==this.getToNode().getDegree()?null:this.getToNode().getOutEdges().getEdges().get(0)===this.getSym()?this.getToNode().getOutEdges().getEdges().get(1):(f.isTrue(this.getToNode().getOutEdges().getEdges().get(1)===this.getSym()),this.getToNode().getOutEdges().getEdges().get(0))},interfaces_:function(){return[]},getClass:function(){return Cr}}),h(Sr,Ir),e(Sr.prototype,{isRemoved:function(){return null===this.dirEdge},setDirectedEdges:function(t,e){this.dirEdge=[t,e],t.setEdge(this),e.setEdge(this),t.setSym(e),e.setSym(t),t.getFromNode().addOutEdge(t),e.getFromNode().addOutEdge(e)},getDirEdge:function(){if(Number.isInteger(arguments[0])){var t=arguments[0];return this.dirEdge[t]}if(arguments[0]instanceof Lr){var e=arguments[0];return this.dirEdge[0].getFromNode()===e?this.dirEdge[0]:this.dirEdge[1].getFromNode()===e?this.dirEdge[1]:null}},remove:function(){this.dirEdge=null},getOppositeNode:function(t){return this.dirEdge[0].getFromNode()===t?this.dirEdge[0].getToNode():this.dirEdge[1].getFromNode()===t?this.dirEdge[1].getToNode():null},interfaces_:function(){return[]},getClass:function(){return Sr}}),e(wr.prototype,{getNextEdge:function(t){var e=this.getIndex(t);return this.outEdges.get(this.getIndex(e+1))},getCoordinate:function(){var t=this.iterator();if(!t.hasNext())return null;var e=t.next();return e.getCoordinate()},iterator:function(){return this.sortEdges(),this.outEdges.iterator()},sortEdges:function(){this.sorted||(ho.sort(this.outEdges),this.sorted=!0)},remove:function(t){this.outEdges.remove(t)},getEdges:function(){return this.sortEdges(),this.outEdges},getNextCWEdge:function(t){var e=this.getIndex(t);return this.outEdges.get(this.getIndex(e-1))},getIndex:function(){if(arguments[0]instanceof Sr){var t=arguments[0];this.sortEdges();for(var e=0;e<this.outEdges.size();e++){var n=this.outEdges.get(e);if(n.getEdge()===t)return e}return-1}if(arguments[0]instanceof Nr){var i=arguments[0];this.sortEdges();for(var e=0;e<this.outEdges.size();e++){var n=this.outEdges.get(e);if(n===i)return e}return-1}if(Number.isInteger(arguments[0])){var r=arguments[0],s=r%this.outEdges.size();return 0>s&&(s+=this.outEdges.size()),s}},add:function(t){this.outEdges.add(t),this.sorted=!1},getDegree:function(){return this.outEdges.size()},interfaces_:function(){return[]},getClass:function(){return wr}}),h(Lr,Ir),e(Lr.prototype,{isRemoved:function(){return null===this.pt},addOutEdge:function(t){this.deStar.add(t)},getCoordinate:function(){return this.pt},getOutEdges:function(){return this.deStar},remove:function(){if(0===arguments.length)this.pt=null;else if(1===arguments.length){var t=arguments[0];this.deStar.remove(t)}},getIndex:function(t){return this.deStar.getIndex(t)},getDegree:function(){return this.deStar.getDegree()},interfaces_:function(){return[]},getClass:function(){return Lr}}),Lr.getEdgesBetween=function(t,e){var n=Nr.toEdges(t.getOutEdges().getEdges()),i=new J(n),r=Nr.toEdges(e.getOutEdges().getEdges());return i.retainAll(r),i},h(Rr,Sr),e(Rr.prototype,{getLine:function(){return this.line},interfaces_:function(){return[]},getClass:function(){return Rr}}),e(Tr.prototype,{find:function(t){return this.nodeMap.get(t)},iterator:function(){return this.nodeMap.values().iterator()},remove:function(t){return this.nodeMap.remove(t)},values:function(){return this.nodeMap.values()},add:function(t){return this.nodeMap.put(t.getCoordinate(),t),t},interfaces_:function(){return[]},getClass:function(){return Tr}}),e(Pr.prototype,{findNodesOfDegree:function(t){for(var e=new I,n=this.nodeIterator();n.hasNext();){var i=n.next();i.getDegree()===t&&e.add(i)}return e},dirEdgeIterator:function(){return this.dirEdges.iterator()},edgeIterator:function(){return this.edges.iterator()},remove:function(){if(arguments[0]instanceof Sr){var t=arguments[0];this.remove(t.getDirEdge(0)),this.remove(t.getDirEdge(1)),this.edges.remove(t),t.remove()}else if(arguments[0]instanceof Nr){var e=arguments[0],n=e.getSym();null!==n&&n.setSym(null),e.getFromNode().remove(e),e.remove(),this.dirEdges.remove(e)}else if(arguments[0]instanceof Lr){for(var i=arguments[0],r=i.getOutEdges().getEdges(),s=r.iterator();s.hasNext();){var o=s.next(),n=o.getSym();null!==n&&this.remove(n),this.dirEdges.remove(o);var a=o.getEdge();null!==a&&this.edges.remove(a)}this.nodeMap.remove(i.getCoordinate()),i.remove()}},findNode:function(t){return this.nodeMap.find(t)},getEdges:function(){return this.edges},nodeIterator:function(){return this.nodeMap.iterator()},contains:function(){if(arguments[0]instanceof Sr){var t=arguments[0];return this.edges.contains(t)}if(arguments[0]instanceof Nr){var e=arguments[0];return this.dirEdges.contains(e)}},add:function(){if(arguments[0]instanceof Lr){var t=arguments[0];this.nodeMap.add(t)}else if(arguments[0]instanceof Sr){var e=arguments[0];this.edges.add(e),this.add(e.getDirEdge(0)),this.add(e.getDirEdge(1))}else if(arguments[0]instanceof Nr){var n=arguments[0];this.dirEdges.add(n)}},getNodes:function(){return this.nodeMap.values()},interfaces_:function(){return[]},getClass:function(){return Pr}}),h(br,Pr),e(br.prototype,{addEdge:function(t){if(t.isEmpty())return null;var e=H.removeRepeatedPoints(t.getCoordinates());if(e.length<=1)return null;var n=e[0],i=e[e.length-1],r=this.getNode(n),s=this.getNode(i),o=new Cr(r,s,e[1],!0),a=new Cr(s,r,e[e.length-2],!1),u=new Rr(t);u.setDirectedEdges(o,a),this.add(u)},getNode:function(t){var e=this.findNode(t);return null===e&&(e=new Lr(t),this.add(e)),e},interfaces_:function(){return[]},getClass:function(){return br}}),e(Or.prototype,{buildEdgeStringsForUnprocessedNodes:function(){for(var t=this.graph.getNodes().iterator();t.hasNext();){var e=t.next();e.isMarked()||(f.isTrue(2===e.getDegree()),this.buildEdgeStringsStartingAt(e),e.setMarked(!0))}},buildEdgeStringsForNonDegree2Nodes:function(){for(var t=this.graph.getNodes().iterator();t.hasNext();){var e=t.next();2!==e.getDegree()&&(this.buildEdgeStringsStartingAt(e),e.setMarked(!0))}},buildEdgeStringsForObviousStartNodes:function(){this.buildEdgeStringsForNonDegree2Nodes()},getMergedLineStrings:function(){return this.merge(),this.mergedLineStrings},buildEdgeStringsStartingAt:function(t){for(var e=t.getOutEdges().iterator();e.hasNext();){var n=e.next();n.getEdge().isMarked()||this.edgeStrings.add(this.buildEdgeStringStartingWith(n))}},merge:function(){if(null!==this.mergedLineStrings)return null;Ir.setMarked(this.graph.nodeIterator(),!1),Ir.setMarked(this.graph.edgeIterator(),!1),this.edgeStrings=new I,this.buildEdgeStringsForObviousStartNodes(),this.buildEdgeStringsForIsolatedLoops(),this.mergedLineStrings=new I;for(var t=this.edgeStrings.iterator();t.hasNext();){var e=t.next();this.mergedLineStrings.add(e.toLineString())}},buildEdgeStringStartingWith:function(t){var e=new Er(this.factory),n=t;do e.add(n),n.getEdge().setMarked(!0),n=n.getNext();while(null!==n&&n!==t);return e},add:function(){if(arguments[0]instanceof B){var t=arguments[0];t.apply({interfaces_:function(){return[q]},filter:function(t){t instanceof St&&this.add(t)}})}else if(R(arguments[0],v)){var e=arguments[0];this.mergedLineStrings=null;for(var n=e.iterator();n.hasNext();){var i=n.next();this.add(i)}}else if(arguments[0]instanceof St){var r=arguments[0];null===this.factory&&(this.factory=r.getFactory()),this.graph.addEdge(r)}},buildEdgeStringsForIsolatedLoops:function(){this.buildEdgeStringsForUnprocessedNodes()},interfaces_:function(){return[]},getClass:function(){return Or}});var Lo=Object.freeze({LineMerger:Or}),Ro=Object.freeze({OverlayOp:ii});h(_r,Nr),e(_r.prototype,{getNext:function(){return this.next},isInRing:function(){return null!==this.edgeRing},setRing:function(t){this.edgeRing=t},setLabel:function(t){this.label=t},getLabel:function(){return this.label},setNext:function(t){this.next=t},getRing:function(){return this.edgeRing},interfaces_:function(){return[]},getClass:function(){return _r}}),h(Mr,Sr),e(Mr.prototype,{getLine:function(){return this.line},interfaces_:function(){return[]},getClass:function(){return Mr}}),e(Dr.prototype,{isIncluded:function(){return this._isIncluded},getCoordinates:function(){if(null===this.ringPts){for(var t=new N,e=this.deList.iterator();e.hasNext();){var n=e.next(),i=n.getEdge();Dr.addEdge(i.getLine().getCoordinates(),n.getEdgeDirection(),t)}this.ringPts=t.toCoordinateArray()}return this.ringPts},isIncludedSet:function(){return this._isIncludedSet},isValid:function(){return this.getCoordinates(),this.ringPts.length<=3?!1:(this.getRing(),this.ring.isValid())},build:function(t){var e=t;do this.add(e),e.setRing(this),e=e.getNext(),f.isTrue(null!==e,"found null DE in ring"),f.isTrue(e===t||!e.isInRing(),"found DE already in ring");while(e!==t)},isOuterHole:function(){return this._isHole?!this.hasShell():!1},getPolygon:function(){var t=null;if(null!==this.holes){t=new Array(this.holes.size()).fill(null);for(var e=0;e<this.holes.size();e++)t[e]=this.holes.get(e)}var n=this.factory.createPolygon(this.ring,t);return n},isHole:function(){return this._isHole},isProcessed:function(){return this._isProcessed},addHole:function(){if(arguments[0]instanceof bt){var t=arguments[0];null===this.holes&&(this.holes=new I),this.holes.add(t)}else if(arguments[0]instanceof Dr){var e=arguments[0];e.setShell(this);var n=e.getRing();null===this.holes&&(this.holes=new I),this.holes.add(n)}},setIncluded:function(t){this._isIncluded=t,this._isIncludedSet=!0},getOuterHole:function(){if(this.isHole())return null;for(var t=0;t<this.deList.size();t++){var e=this.deList.get(t),n=e.getSym().getRing();if(n.isOuterHole())return n}return null},computeHole:function(){var t=this.getRing();this._isHole=he.isCCW(t.getCoordinates())},hasShell:function(){return null!==this.shell},isOuterShell:function(){return null!==this.getOuterHole()},getLineString:function(){return this.getCoordinates(),this.factory.createLineString(this.ringPts)},toString:function(){return se.toLineString(new Gt(this.getCoordinates()))},getShell:function(){return this.isHole()?this.shell:this},add:function(t){this.deList.add(t)},getRing:function(){if(null!==this.ring)return this.ring;this.getCoordinates(),this.ringPts.length<3&&A.out.println(this.ringPts);try{this.ring=this.factory.createLinearRing(this.ringPts)}catch(t){if(!(t instanceof S))throw t;A.out.println(this.ringPts)}finally{}return this.ring},updateIncluded:function(){if(this.isHole())return null;for(var t=0;t<this.deList.size();t++){var e=this.deList.get(t),n=e.getSym().getRing().getShell();if(null!==n&&n.isIncludedSet())return this.setIncluded(!n.isIncluded()),null}},setShell:function(t){this.shell=t},setProcessed:function(t){this._isProcessed=t},interfaces_:function(){return[]},getClass:function(){return Dr}}),Dr.findDirEdgesInRing=function(t){var e=t,n=new I;do n.add(e),e=e.getNext(),f.isTrue(null!==e,"found null DE in ring"),f.isTrue(e===t||!e.isInRing(),"found DE already in ring");while(e!==t);return n},Dr.addEdge=function(t,e,n){if(e)for(var i=0;i<t.length;i++)n.add(t[i],!1);else for(var i=t.length-1;i>=0;i--)n.add(t[i],!1)},Dr.findEdgeRingContaining=function(t,e){for(var n=t.getRing(),i=n.getEnvelopeInternal(),r=n.getCoordinateN(0),s=null,o=null,a=e.iterator();a.hasNext();){var u=a.next(),l=u.getRing(),h=l.getEnvelopeInternal();if(!h.equals(i)&&h.contains(i)){r=H.ptNotInList(n.getCoordinates(),l.getCoordinates());var c=!1;he.isPointInRing(r,l.getCoordinates())&&(c=!0),c&&(null===s||o.contains(h))&&(s=u,o=s.getRing().getEnvelopeInternal())}}return s},e(Ar.prototype,{compare:function(t,e){var n=t,i=e;return n.getRing().getEnvelope().compareTo(i.getRing().getEnvelope())},interfaces_:function(){return[a]},getClass:function(){return Ar}}),Dr.EnvelopeComparator=Ar,h(Fr,Pr),e(Fr.prototype,{findEdgeRing:function(t){var e=new Dr(this.factory);return e.build(t),e},computeDepthParity:function(){if(0===arguments.length)for(;;){var t=null;if(null===t)return null;this.computeDepthParity(t)}else if(1===arguments.length){arguments[0]}},computeNextCWEdges:function(){for(var t=this.nodeIterator();t.hasNext();){var e=t.next();Fr.computeNextCWEdges(e)}},addEdge:function(t){if(t.isEmpty())return null;var e=H.removeRepeatedPoints(t.getCoordinates());if(e.length<2)return null;var n=e[0],i=e[e.length-1],r=this.getNode(n),s=this.getNode(i),o=new _r(r,s,e[1],!0),a=new _r(s,r,e[e.length-2],!1),u=new Mr(t);u.setDirectedEdges(o,a),this.add(u)},deleteCutEdges:function(){this.computeNextCWEdges(),Fr.findLabeledEdgeRings(this.dirEdges);for(var t=new I,e=this.dirEdges.iterator();e.hasNext();){var n=e.next();if(!n.isMarked()){var i=n.getSym();if(n.getLabel()===i.getLabel()){n.setMarked(!0),i.setMarked(!0);var r=n.getEdge();t.add(r.getLine())}}}return t},getEdgeRings:function(){this.computeNextCWEdges(),Fr.label(this.dirEdges,-1);var t=Fr.findLabeledEdgeRings(this.dirEdges);this.convertMaximalToMinimalEdgeRings(t);for(var e=new I,n=this.dirEdges.iterator();n.hasNext();){var i=n.next();if(!i.isMarked()&&!i.isInRing()){var r=this.findEdgeRing(i);e.add(r)}}return e},getNode:function(t){var e=this.findNode(t);return null===e&&(e=new Lr(t),this.add(e)),e},convertMaximalToMinimalEdgeRings:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next(),i=n.getLabel(),r=Fr.findIntersectionNodes(n,i);if(null!==r)for(var s=r.iterator();s.hasNext();){var o=s.next();Fr.computeNextCCWEdges(o,i)}}},deleteDangles:function(){for(var t=this.findNodesOfDegree(1),e=new J,n=new pe,i=t.iterator();i.hasNext();)n.push(i.next());for(;!n.isEmpty();){var r=n.pop();Fr.deleteAllEdges(r);for(var s=r.getOutEdges().getEdges(),i=s.iterator();i.hasNext();){var o=i.next();o.setMarked(!0);var a=o.getSym();null!==a&&a.setMarked(!0);var u=o.getEdge();e.add(u.getLine());var l=o.getToNode();1===Fr.getDegreeNonDeleted(l)&&n.push(l)}}return e},interfaces_:function(){return[]},getClass:function(){return Fr}}),Fr.findLabeledEdgeRings=function(t){for(var e=new I,n=1,i=t.iterator();i.hasNext();){var r=i.next();if(!(r.isMarked()||r.getLabel()>=0)){e.add(r);var s=Dr.findDirEdgesInRing(r);Fr.label(s,n),n++}}return e},Fr.getDegreeNonDeleted=function(t){for(var e=t.getOutEdges().getEdges(),n=0,i=e.iterator();i.hasNext();){var r=i.next();r.isMarked()||n++}return n},Fr.deleteAllEdges=function(t){for(var e=t.getOutEdges().getEdges(),n=e.iterator();n.hasNext();){var i=n.next();i.setMarked(!0);var r=i.getSym();null!==r&&r.setMarked(!0)}},Fr.label=function(t,e){for(var n=t.iterator();n.hasNext();){var i=n.next();i.setLabel(e)}},Fr.computeNextCWEdges=function(t){for(var e=t.getOutEdges(),n=null,i=null,r=e.getEdges().iterator();r.hasNext();){var s=r.next();if(!s.isMarked()){if(null===n&&(n=s),null!==i){var o=i.getSym();o.setNext(s)}i=s}}if(null!==i){var o=i.getSym();o.setNext(n)}},Fr.computeNextCCWEdges=function(t,e){for(var n=t.getOutEdges(),i=null,r=null,s=n.getEdges(),o=s.size()-1;o>=0;o--){var a=s.get(o),u=a.getSym(),l=null;a.getLabel()===e&&(l=a);var h=null;u.getLabel()===e&&(h=u),null===l&&null===h||(null!==h&&(r=h),null!==l&&(null!==r&&(r.setNext(l),r=null),null===i&&(i=l)))}null!==r&&(f.isTrue(null!==i),r.setNext(i))},Fr.getDegree=function(t,e){for(var n=t.getOutEdges().getEdges(),i=0,r=n.iterator();r.hasNext();){var s=r.next();s.getLabel()===e&&i++}return i},Fr.findIntersectionNodes=function(t,e){var n=t,i=null;do{var r=n.getFromNode();Fr.getDegree(r,e)>1&&(null===i&&(i=new I),i.add(r)),n=n.getNext(),f.isTrue(null!==n,"found null DE in ring"),f.isTrue(n===t||!n.isInRing(),"found DE already in ring")}while(n!==t);return i},e(Gr.prototype,{getGeometry:function(){return null===this.geomFactory&&(this.geomFactory=new ie),this.polygonize(),this.extractOnlyPolygonal?this.geomFactory.buildGeometry(this.polyList):this.geomFactory.createGeometryCollection(ie.toGeometryArray(this.polyList))},getInvalidRingLines:function(){return this.polygonize(),this.invalidRingLines},findValidRings:function(t,e,n){for(var i=t.iterator();i.hasNext();){var r=i.next();r.isValid()?e.add(r):n.add(r.getLineString())}},polygonize:function(){if(null!==this.polyList)return null;if(this.polyList=new I,null===this.graph)return null;this.dangles=this.graph.deleteDangles(),this.cutEdges=this.graph.deleteCutEdges();var t=this.graph.getEdgeRings(),e=new I;this.invalidRingLines=new I,this.isCheckingRingsValid?this.findValidRings(t,e,this.invalidRingLines):e=t,this.findShellsAndHoles(e),Gr.assignHolesToShells(this.holeList,this.shellList),ho.sort(this.shellList,new Dr.EnvelopeComparator);var n=!0;this.extractOnlyPolygonal&&(Gr.findDisjointShells(this.shellList),n=!1),this.polyList=Gr.extractPolygons(this.shellList,n)},getDangles:function(){return this.polygonize(),this.dangles},getCutEdges:function(){return this.polygonize(),this.cutEdges},getPolygons:function(){return this.polygonize(),this.polyList},add:function(){if(R(arguments[0],v))for(var t=arguments[0],e=t.iterator();e.hasNext();){var n=e.next();this.add(n)}else if(arguments[0]instanceof St){var i=arguments[0];this.geomFactory=i.getFactory(),null===this.graph&&(this.graph=new Fr(this.geomFactory)),this.graph.addEdge(i)}else if(arguments[0]instanceof B){var r=arguments[0];r.apply(this.lineStringAdder)}},setCheckRingsValid:function(t){this.isCheckingRingsValid=t},findShellsAndHoles:function(t){this.holeList=new I,this.shellList=new I;for(var e=t.iterator();e.hasNext();){var n=e.next();n.computeHole(),n.isHole()?this.holeList.add(n):this.shellList.add(n)}},interfaces_:function(){return[]},getClass:function(){return Gr}}),Gr.findOuterShells=function(t){for(var e=t.iterator();e.hasNext();){var n=e.next(),i=n.getOuterHole();null===i||i.isProcessed()||(n.setIncluded(!0),i.setProcessed(!0))}},Gr.extractPolygons=function(t,e){for(var n=new I,i=t.iterator();i.hasNext();){var r=i.next();(e||r.isIncluded())&&n.add(r.getPolygon())}return n},Gr.assignHolesToShells=function(t,e){for(var n=t.iterator();n.hasNext();){var i=n.next();Gr.assignHoleToShell(i,e)}},Gr.assignHoleToShell=function(t,e){var n=Dr.findEdgeRingContaining(t,e);null!==n&&n.addHole(t)},Gr.findDisjointShells=function(t){Gr.findOuterShells(t);var e=null;do{e=!1;for(var n=t.iterator();n.hasNext();){var i=n.next();i.isIncludedSet()||(i.updateIncluded(),i.isIncludedSet()||(e=!0))}}while(e)},e(qr.prototype,{filter:function(t){t instanceof St&&this.p.add(t)},interfaces_:function(){return[q]},getClass:function(){return qr}}),Gr.LineStringAdder=qr;var To=Object.freeze({Polygonizer:Gr});e(Br.prototype,{createEdgeEndForNext:function(t,e,n,i){var r=n.segmentIndex+1;if(r>=t.getNumPoints()&&null===i)return null;var s=t.getCoordinate(r);null!==i&&i.segmentIndex===n.segmentIndex&&(s=i.coord);var o=new En(t,n.coord,s,new gn(t.getLabel()));e.add(o)},createEdgeEndForPrev:function(t,e,n,i){var r=n.segmentIndex;if(0===n.dist){if(0===r)return null;r--}var s=t.getCoordinate(r);null!==i&&i.segmentIndex>=r&&(s=i.coord);var o=new gn(t.getLabel());o.flip();var a=new En(t,n.coord,s,o);e.add(a)},computeEdgeEnds:function(){if(1===arguments.length){for(var t=arguments[0],e=new I,n=t;n.hasNext();){var i=n.next();this.computeEdgeEnds(i,e)}return e}if(2===arguments.length){var r=arguments[0],s=arguments[1],o=r.getEdgeIntersectionList();o.addEndpoints();var a=o.iterator(),u=null,l=null;if(!a.hasNext())return null;var h=a.next();do u=l,l=h,h=null,a.hasNext()&&(h=a.next()),null!==l&&(this.createEdgeEndForPrev(r,s,l,u),this.createEdgeEndForNext(r,s,l,h));while(null!==l)}},interfaces_:function(){return[]},getClass:function(){return Br}}),h(zr,En),e(zr.prototype,{insert:function(t){this.edgeEnds.add(t)},print:function(t){t.println("EdgeEndBundle--> Label: "+this.label);for(var e=this.iterator();e.hasNext();){var n=e.next();n.print(t),t.println()}},iterator:function(){return this.edgeEnds.iterator()},getEdgeEnds:function(){return this.edgeEnds},computeLabelOn:function(t,e){for(var n=0,i=!1,r=this.iterator();r.hasNext();){var s=r.next(),o=s.getLabel().getLocation(t);o===L.BOUNDARY&&n++,o===L.INTERIOR&&(i=!0)}var o=L.NONE;i&&(o=L.INTERIOR),n>0&&(o=$n.determineBoundary(e,n)),this.label.setLocation(t,o)},computeLabelSide:function(t,e){for(var n=this.iterator();n.hasNext();){var i=n.next();if(i.getLabel().isArea()){var r=i.getLabel().getLocation(t,e);if(r===L.INTERIOR)return this.label.setLocation(t,e,L.INTERIOR),null;r===L.EXTERIOR&&this.label.setLocation(t,e,L.EXTERIOR)}}},getLabel:function(){return this.label},computeLabelSides:function(t){this.computeLabelSide(t,cn.LEFT),this.computeLabelSide(t,cn.RIGHT)},updateIM:function(t){Jn.updateIM(this.label,t)},computeLabel:function(t){for(var e=!1,n=this.iterator();n.hasNext();){var i=n.next();i.getLabel().isArea()&&(e=!0)}e?this.label=new gn(L.NONE,L.NONE,L.NONE):this.label=new gn(L.NONE);for(var r=0;2>r;r++)this.computeLabelOn(r,t),e&&this.computeLabelSides(r)},interfaces_:function(){return[]},getClass:function(){return zr}}),h(Vr,Pn),e(Vr.prototype,{updateIM:function(t){for(var e=this.iterator();e.hasNext();){var n=e.next();n.updateIM(t)}},insert:function(t){var e=this.edgeMap.get(t);null===e?(e=new zr(t),this.insertEdgeEnd(t,e)):e.insert(t)},interfaces_:function(){return[]},getClass:function(){return Vr}}),h(kr,yn),e(kr.prototype,{updateIMFromEdges:function(t){this.edges.updateIM(t)},computeIM:function(t){t.setAtLeastIfValid(this.label.getLocation(0),this.label.getLocation(1),0)},interfaces_:function(){return[]},getClass:function(){return kr}}),h(Yr,Nn),e(Yr.prototype,{createNode:function(t){return new kr(t,new Vr)},interfaces_:function(){return[]},getClass:function(){return Yr}}),e(Ur.prototype,{insertEdgeEnds:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.nodes.add(n)}},computeProperIntersectionIM:function(t,e){var n=this.arg[0].getGeometry().getDimension(),i=this.arg[1].getGeometry().getDimension(),r=t.hasProperIntersection(),s=t.hasProperInteriorIntersection();2===n&&2===i?r&&e.setAtLeast("212101212"):2===n&&1===i?(r&&e.setAtLeast("FFF0FFFF2"),s&&e.setAtLeast("1FFFFF1FF")):1===n&&2===i?(r&&e.setAtLeast("F0FFFFFF2"),s&&e.setAtLeast("1F1FFFFFF")):1===n&&1===i&&s&&e.setAtLeast("0FFFFFFFF")},labelIsolatedEdges:function(t,e){for(var n=this.arg[t].getEdgeIterator();n.hasNext();){var i=n.next();i.isIsolated()&&(this.labelIsolatedEdge(i,e,this.arg[e].getGeometry()),this.isolatedEdges.add(i))}},labelIsolatedEdge:function(t,e,n){if(n.getDimension()>0){var i=this.ptLocator.locate(t.getCoordinate(),n);t.getLabel().setAllLocations(e,i)}else t.getLabel().setAllLocations(e,L.EXTERIOR)},computeIM:function(){var t=new fe;if(t.set(L.EXTERIOR,L.EXTERIOR,2),!this.arg[0].getGeometry().getEnvelopeInternal().intersects(this.arg[1].getGeometry().getEnvelopeInternal()))return this.computeDisjointIM(t),t;this.arg[0].computeSelfNodes(this.li,!1),this.arg[1].computeSelfNodes(this.li,!1);var e=this.arg[0].computeEdgeIntersections(this.arg[1],this.li,!1);this.computeIntersectionNodes(0),this.computeIntersectionNodes(1),this.copyNodesAndLabels(0),this.copyNodesAndLabels(1),this.labelIsolatedNodes(),this.computeProperIntersectionIM(e,t);var n=new Br,i=n.computeEdgeEnds(this.arg[0].getEdgeIterator());this.insertEdgeEnds(i);var r=n.computeEdgeEnds(this.arg[1].getEdgeIterator());return this.insertEdgeEnds(r),this.labelNodeEdges(),this.labelIsolatedEdges(0,1),this.labelIsolatedEdges(1,0),this.updateIM(t),t},labelNodeEdges:function(){for(var t=this.nodes.iterator();t.hasNext();){var e=t.next();e.getEdges().computeLabelling(this.arg)}},copyNodesAndLabels:function(t){for(var e=this.arg[t].getNodeIterator();e.hasNext();){var n=e.next(),i=this.nodes.addNode(n.getCoordinate());i.setLabel(t,n.getLabel().getLocation(t))}},labelIntersectionNodes:function(t){for(var e=this.arg[t].getEdgeIterator();e.hasNext();)for(var n=e.next(),i=n.getLabel().getLocation(t),r=n.getEdgeIntersectionList().iterator();r.hasNext();){var s=r.next(),o=this.nodes.find(s.coord);o.getLabel().isNull(t)&&(i===L.BOUNDARY?o.setLabelBoundary(t):o.setLabel(t,L.INTERIOR))}},labelIsolatedNode:function(t,e){var n=this.ptLocator.locate(t.getCoordinate(),this.arg[e].getGeometry());t.getLabel().setAllLocations(e,n)},computeIntersectionNodes:function(t){for(var e=this.arg[t].getEdgeIterator();e.hasNext();)for(var n=e.next(),i=n.getLabel().getLocation(t),r=n.getEdgeIntersectionList().iterator();r.hasNext();){var s=r.next(),o=this.nodes.addNode(s.coord);i===L.BOUNDARY?o.setLabelBoundary(t):o.getLabel().isNull(t)&&o.setLabel(t,L.INTERIOR)}},labelIsolatedNodes:function(){for(var t=this.nodes.iterator();t.hasNext();){var e=t.next(),n=e.getLabel();f.isTrue(n.getGeometryCount()>0,"node with empty label found"),e.isIsolated()&&(n.isNull(0)?this.labelIsolatedNode(e,0):this.labelIsolatedNode(e,1))}},updateIM:function(t){for(var e=this.isolatedEdges.iterator();e.hasNext();){var n=e.next();n.updateIM(t)}for(var i=this.nodes.iterator();i.hasNext();){var r=i.next();r.updateIM(t),r.updateIMFromEdges(t)}},computeDisjointIM:function(t){var e=this.arg[0].getGeometry();e.isEmpty()||(t.set(L.INTERIOR,L.EXTERIOR,e.getDimension()),t.set(L.BOUNDARY,L.EXTERIOR,e.getBoundaryDimension()));var n=this.arg[1].getGeometry();n.isEmpty()||(t.set(L.EXTERIOR,L.INTERIOR,n.getDimension()),t.set(L.EXTERIOR,L.BOUNDARY,n.getBoundaryDimension()))},interfaces_:function(){return[]},getClass:function(){return Ur}}),e(Xr.prototype,{isContainedInBoundary:function(t){if(t instanceof Tt)return!1;if(t instanceof Lt)return this.isPointContainedInBoundary(t);if(t instanceof St)return this.isLineStringContainedInBoundary(t);for(var e=0;e<t.getNumGeometries();e++){var n=t.getGeometryN(e);if(!this.isContainedInBoundary(n))return!1}return!0},isLineSegmentContainedInBoundary:function(t,e){if(t.equals(e))return this.isPointContainedInBoundary(t);if(t.x===e.x){if(t.x===this.rectEnv.getMinX()||t.x===this.rectEnv.getMaxX())return!0}else if(t.y===e.y&&(t.y===this.rectEnv.getMinY()||t.y===this.rectEnv.getMaxY()))return!0;return!1},isLineStringContainedInBoundary:function(t){for(var e=t.getCoordinateSequence(),n=new g,i=new g,r=0;r<e.size()-1;r++)if(e.getCoordinate(r,n),e.getCoordinate(r+1,i),!this.isLineSegmentContainedInBoundary(n,i))return!1;return!0},isPointContainedInBoundary:function(){if(arguments[0]instanceof Lt){var t=arguments[0];return this.isPointContainedInBoundary(t.getCoordinate())}if(arguments[0]instanceof g){var e=arguments[0];return e.x===this.rectEnv.getMinX()||e.x===this.rectEnv.getMaxX()||e.y===this.rectEnv.getMinY()||e.y===this.rectEnv.getMaxY()}},contains:function(t){return this.rectEnv.contains(t.getEnvelopeInternal())?!this.isContainedInBoundary(t):!1},interfaces_:function(){return[]},getClass:function(){return Xr}}),Xr.contains=function(t,e){var n=new Xr(t);return n.contains(e)},e(Hr.prototype,{intersects:function(t,e){var n=new C(t,e);if(!this.rectEnv.intersects(n))return!1;if(this.rectEnv.intersects(t))return!0;if(this.rectEnv.intersects(e))return!0;if(t.compareTo(e)>0){var i=t;t=e,e=i}var r=!1;return e.y>t.y&&(r=!0),r?this.li.computeIntersection(t,e,this.diagDown0,this.diagDown1):this.li.computeIntersection(t,e,this.diagUp0,this.diagUp1),!!this.li.hasIntersection()},interfaces_:function(){return[]},getClass:function(){return Hr}}),e(Wr.prototype,{applyTo:function(t){for(var e=0;e<t.getNumGeometries()&&!this._isDone;e++){var n=t.getGeometryN(e);if(n instanceof ft)this.applyTo(n);else if(this.visit(n),this.isDone())return this._isDone=!0,null}},interfaces_:function(){return[]},getClass:function(){return Wr}}),e(jr.prototype,{intersects:function(t){if(!this.rectEnv.intersects(t.getEnvelopeInternal()))return!1;var e=new Kr(this.rectEnv);if(e.applyTo(t),e.intersects())return!0;var n=new Zr(this.rectangle);if(n.applyTo(t),n.containsPoint())return!0;var i=new Qr(this.rectangle);return i.applyTo(t),!!i.intersects()},interfaces_:function(){return[]},getClass:function(){return jr}}),jr.intersects=function(t,e){var n=new jr(t);return n.intersects(e)},h(Kr,Wr),e(Kr.prototype,{isDone:function(){return this._intersects===!0},visit:function(t){var e=t.getEnvelopeInternal();return this.rectEnv.intersects(e)?this.rectEnv.contains(e)?(this._intersects=!0,null):e.getMinX()>=this.rectEnv.getMinX()&&e.getMaxX()<=this.rectEnv.getMaxX()?(this._intersects=!0,null):e.getMinY()>=this.rectEnv.getMinY()&&e.getMaxY()<=this.rectEnv.getMaxY()?(this._intersects=!0,null):void 0:null},intersects:function(){return this._intersects},interfaces_:function(){return[]},getClass:function(){return Kr}}),h(Zr,Wr),e(Zr.prototype,{isDone:function(){return this._containsPoint===!0},visit:function(t){if(!(t instanceof Tt))return null;var e=t.getEnvelopeInternal();if(!this.rectEnv.intersects(e))return null;for(var n=new g,i=0;4>i;i++)if(this.rectSeq.getCoordinate(i,n),e.contains(n)&&Tn.containsPointInPolygon(n,t))return this._containsPoint=!0,null},containsPoint:function(){return this._containsPoint},interfaces_:function(){return[]},getClass:function(){return Zr}}),h(Qr,Wr),e(Qr.prototype,{intersects:function(){return this.hasIntersection},isDone:function(){return this.hasIntersection===!0},visit:function(t){var e=t.getEnvelopeInternal();if(!this.rectEnv.intersects(e))return null;var n=kn.getLines(t);this.checkIntersectionWithLineStrings(n)},checkIntersectionWithLineStrings:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();if(this.checkIntersectionWithSegments(n),this.hasIntersection)return null}},checkIntersectionWithSegments:function(t){for(var e=t.getCoordinateSequence(),n=1;n<e.size();n++)if(e.getCoordinate(n-1,this.p0),e.getCoordinate(n,this.p1),this.rectIntersector.intersects(this.p0,this.p1))return this.hasIntersection=!0,null},interfaces_:function(){return[]},getClass:function(){return Qr}}),h(Jr,ti),e(Jr.prototype,{getIntersectionMatrix:function(){return this._relate.computeIM()},interfaces_:function(){return[]},getClass:function(){return Jr}}),Jr.covers=function(t,e){return t.getEnvelopeInternal().covers(e.getEnvelopeInternal())?t.isRectangle()?!0:Jr.relate(t,e).isCovers():!1},Jr.intersects=function(t,e){return t.getEnvelopeInternal().intersects(e.getEnvelopeInternal())?t.isRectangle()?jr.intersects(t,e):e.isRectangle()?jr.intersects(e,t):Jr.relate(t,e).isIntersects():!1},Jr.touches=function(t,e){return t.getEnvelopeInternal().intersects(e.getEnvelopeInternal())?Jr.relate(t,e).isTouches(t.getDimension(),e.getDimension()):!1},Jr.within=function(t,e){return e.contains(t)},Jr.coveredBy=function(t,e){return Jr.covers(e,t)},Jr.relate=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=new Jr(t,e),i=n.getIntersectionMatrix();return i}if(3===arguments.length){if("string"==typeof arguments[2]&&arguments[0]instanceof B&&arguments[1]instanceof B){var r=arguments[0],s=arguments[1],o=arguments[2];return Jr.relateWithCheck(r,s).matches(o)}if(R(arguments[2],V)&&arguments[0]instanceof B&&arguments[1]instanceof B){var a=arguments[0],u=arguments[1],l=arguments[2],n=new Jr(a,u,l),i=n.getIntersectionMatrix();return i}}},Jr.overlaps=function(t,e){return t.getEnvelopeInternal().intersects(e.getEnvelopeInternal())?Jr.relate(t,e).isOverlaps(t.getDimension(),e.getDimension()):!1},Jr.disjoint=function(t,e){return!t.intersects(e)},Jr.relateWithCheck=function(t,e){return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),Jr.relate(t,e)},Jr.crosses=function(t,e){return t.getEnvelopeInternal().intersects(e.getEnvelopeInternal())?Jr.relate(t,e).isCrosses(t.getDimension(),e.getDimension()):!1},Jr.contains=function(t,e){return t.getEnvelopeInternal().contains(e.getEnvelopeInternal())?t.isRectangle()?Xr.contains(t,e):Jr.relate(t,e).isContains():!1};var Po=Object.freeze({RelateOp:Jr});e($r.prototype,{extractElements:function(t,e){if(null===t)return null;for(var n=0;n<t.getNumGeometries();n++){var i=t.getGeometryN(n);this.skipEmpty&&i.isEmpty()||e.add(i)}},combine:function(){for(var t=new I,e=this.inputGeoms.iterator();e.hasNext();){var n=e.next();this.extractElements(n,t)}return 0===t.size()?null!==this.geomFactory?this.geomFactory.createGeometryCollection(null):null:this.geomFactory.buildGeometry(t)},interfaces_:function(){return[]},getClass:function(){return $r}}),$r.combine=function(){if(1===arguments.length){var t=arguments[0],e=new $r(t);
return e.combine()}if(2===arguments.length){var n=arguments[0],i=arguments[1],e=new $r($r.createList(n,i));return e.combine()}if(3===arguments.length){var r=arguments[0],s=arguments[1],o=arguments[2],e=new $r($r.createList(r,s,o));return e.combine()}},$r.extractFactory=function(t){return t.isEmpty()?null:t.iterator().next().getFactory()},$r.createList=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=new I;return n.add(t),n.add(e),n}if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2],n=new I;return n.add(i),n.add(r),n.add(s),n}},e(ts.prototype,{union:function(){for(var t=new Te,e=new at,n=0;n<this.pointGeom.getNumGeometries();n++){var i=this.pointGeom.getGeometryN(n),r=i.getCoordinate(),s=t.locate(r,this.otherGeom);s===L.EXTERIOR&&e.add(r)}if(0===e.size())return this.otherGeom;var o=null,a=H.toCoordinateArray(e);return o=1===a.length?this.geomFact.createPoint(a[0]):this.geomFact.createMultiPointFromCoords(a),$r.combine(o,this.otherGeom)},interfaces_:function(){return[]},getClass:function(){return ts}}),ts.union=function(t,e){var n=new ts(t,e);return n.union()},e(es.prototype,{filter:function(t){-1!==this.sortIndex&&t.getSortIndex()!==this.sortIndex||this.comps.add(t)},interfaces_:function(){return[ht]},getClass:function(){return es}}),es.extract=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return es.extract(t,e,new I)}if(3===arguments.length){var n=arguments[0],i=arguments[1],r=arguments[2];return n.getSortIndex()===i?r.add(n):n instanceof ft&&n.apply(new es(i,r)),r}},e(ns.prototype,{reduceToGeometries:function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next(),r=null;R(i,y)?r=this.unionTree(i):i instanceof B&&(r=i),e.add(r)}return e},extractByEnvelope:function(t,e,n){for(var i=new I,r=0;r<e.getNumGeometries();r++){var s=e.getGeometryN(r);s.getEnvelopeInternal().intersects(t)?i.add(s):n.add(s)}return this.geomFactory.buildGeometry(i)},unionOptimized:function(t,e){var n=t.getEnvelopeInternal(),i=e.getEnvelopeInternal();if(!n.intersects(i)){var r=$r.combine(t,e);return r}if(t.getNumGeometries()<=1&&e.getNumGeometries()<=1)return this.unionActual(t,e);var s=n.intersection(i);return this.unionUsingEnvelopeIntersection(t,e,s)},union:function(){if(null===this.inputPolys)throw new IllegalStateException("union() method cannot be called twice");if(this.inputPolys.isEmpty())return null;this.geomFactory=this.inputPolys.iterator().next().getFactory();for(var t=new ke(ns.STRTREE_NODE_CAPACITY),e=this.inputPolys.iterator();e.hasNext();){var n=e.next();t.insert(n.getEnvelopeInternal(),n)}this.inputPolys=null;var i=t.itemsTree(),r=this.unionTree(i);return r},binaryUnion:function(){if(1===arguments.length){var t=arguments[0];return this.binaryUnion(t,0,t.size())}if(3===arguments.length){var e=arguments[0],n=arguments[1],i=arguments[2];if(1>=i-n){var r=ns.getGeometry(e,n);return this.unionSafe(r,null)}if(i-n===2)return this.unionSafe(ns.getGeometry(e,n),ns.getGeometry(e,n+1));var s=Math.trunc((i+n)/2),r=this.binaryUnion(e,n,s),o=this.binaryUnion(e,s,i);return this.unionSafe(r,o)}},repeatedUnion:function(t){for(var e=null,n=t.iterator();n.hasNext();){var i=n.next();e=null===e?i.copy():e.union(i)}return e},unionSafe:function(t,e){return null===t&&null===e?null:null===t?e.copy():null===e?t.copy():this.unionOptimized(t,e)},unionActual:function(t,e){return ns.restrictToPolygons(t.union(e))},unionTree:function(t){var e=this.reduceToGeometries(t),n=this.binaryUnion(e);return n},unionUsingEnvelopeIntersection:function(t,e,n){var i=new I,r=this.extractByEnvelope(n,t,i),s=this.extractByEnvelope(n,e,i),o=this.unionActual(r,s);i.add(o);var a=$r.combine(i);return a},bufferUnion:function(){if(1===arguments.length){var t=arguments[0],e=t.get(0).getFactory(),n=e.buildGeometry(t),i=n.buffer(0);return i}if(2===arguments.length){var r=arguments[0],s=arguments[1],e=r.getFactory(),n=e.createGeometryCollection([r,s]),i=n.buffer(0);return i}},interfaces_:function(){return[]},getClass:function(){return ns}}),ns.restrictToPolygons=function(t){if(R(t,Rt))return t;var e=pr.getPolygons(t);return 1===e.size()?e.get(0):t.getFactory().createMultiPolygon(ie.toPolygonArray(e))},ns.getGeometry=function(t,e){return e>=t.size()?null:t.get(e)},ns.union=function(t){var e=new ns(t);return e.union()},ns.STRTREE_NODE_CAPACITY=4,e(is.prototype,{unionNoOpt:function(t){var e=this.geomFact.createPoint();return si.overlayOp(t,e,ii.UNION)},unionWithNull:function(t,e){return null===t&&null===e?null:null===e?t:null===t?e:t.union(e)},extract:function(){if(R(arguments[0],v))for(var t=arguments[0],e=t.iterator();e.hasNext();){var n=e.next();this.extract(n)}else if(arguments[0]instanceof B){var i=arguments[0];null===this.geomFact&&(this.geomFact=i.getFactory()),es.extract(i,B.SORTINDEX_POLYGON,this.polygons),es.extract(i,B.SORTINDEX_LINESTRING,this.lines),es.extract(i,B.SORTINDEX_POINT,this.points)}},union:function Xo(){if(null===this.geomFact)return null;var t=null;if(this.points.size()>0){var e=this.geomFact.buildGeometry(this.points);t=this.unionNoOpt(e)}var n=null;if(this.lines.size()>0){var i=this.geomFact.buildGeometry(this.lines);n=this.unionNoOpt(i)}var r=null;this.polygons.size()>0&&(r=ns.union(this.polygons));var s=this.unionWithNull(n,r),Xo=null;return Xo=null===t?s:null===s?t:ts.union(t,s),null===Xo?this.geomFact.createGeometryCollection():Xo},interfaces_:function(){return[]},getClass:function(){return is}}),is.union=function(){if(1===arguments.length){if(R(arguments[0],v)){var t=arguments[0],e=new is(t);return e.union()}if(arguments[0]instanceof B){var n=arguments[0],e=new is(n);return e.union()}}else if(2===arguments.length){var i=arguments[0],r=arguments[1],e=new is(i,r);return e.union()}};var bo=Object.freeze({UnaryUnionOp:is});e(rs.prototype,{visitInteriorRing:function(t,e){var n=t.getCoordinates(),i=n[0],r=rs.findDifferentPoint(n,i),s=e.findEdgeInSameDirection(i,r),o=e.findEdgeEnd(s),a=null;o.getLabel().getLocation(0,cn.RIGHT)===L.INTERIOR?a=o:o.getSym().getLabel().getLocation(0,cn.RIGHT)===L.INTERIOR&&(a=o.getSym()),f.isTrue(null!==a,"unable to find dirEdge with Interior on RHS"),this.visitLinkedDirectedEdges(a)},visitShellInteriors:function(t,e){if(t instanceof Tt){var n=t;this.visitInteriorRing(n.getExteriorRing(),e)}if(t instanceof Ot)for(var i=t,r=0;r<i.getNumGeometries();r++){var n=i.getGeometryN(r);this.visitInteriorRing(n.getExteriorRing(),e)}},getCoordinate:function(){return this.disconnectedRingcoord},setInteriorEdgesInResult:function(t){for(var e=t.getEdgeEnds().iterator();e.hasNext();){var n=e.next();n.getLabel().getLocation(0,cn.RIGHT)===L.INTERIOR&&n.setInResult(!0)}},visitLinkedDirectedEdges:function(t){var e=t,n=t;do f.isTrue(null!==n,"found null Directed Edge"),n.setVisited(!0),n=n.getNext();while(n!==e)},buildEdgeRings:function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next();if(i.isInResult()&&null===i.getEdgeRing()){var r=new vn(i,this.geometryFactory);r.linkDirectedEdgesForMinimalEdgeRings();var s=r.buildMinimalRings();e.addAll(s)}}return e},hasUnvisitedShellEdge:function(t){for(var e=0;e<t.size();e++){var n=t.get(e);if(!n.isHole()){var i=n.getEdges(),r=i.get(0);if(r.getLabel().getLocation(0,cn.RIGHT)===L.INTERIOR)for(var s=0;s<i.size();s++)if(r=i.get(s),!r.isVisited())return this.disconnectedRingcoord=r.getCoordinate(),!0}}return!1},isInteriorsConnected:function(){var t=new I;this.geomGraph.computeSplitEdges(t);var e=new Cn(new On);e.addEdges(t),this.setInteriorEdgesInResult(e),e.linkResultDirectedEdges();var n=this.buildEdgeRings(e.getEdgeEnds());return this.visitShellInteriors(this.geomGraph.getGeometry(),e),!this.hasUnvisitedShellEdge(n)},interfaces_:function(){return[]},getClass:function(){return rs}}),rs.findDifferentPoint=function(t,e){for(var n=0;n<t.length;n++)if(!t[n].equals(e))return t[n];return null},e(ss.prototype,{insertEdgeEnds:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.nodes.add(n)}},getNodeIterator:function(){return this.nodes.iterator()},copyNodesAndLabels:function(t,e){for(var n=t.getNodeIterator();n.hasNext();){var i=n.next(),r=this.nodes.addNode(i.getCoordinate());r.setLabel(e,i.getLabel().getLocation(e))}},build:function(t){this.computeIntersectionNodes(t,0),this.copyNodesAndLabels(t,0);var e=new Br,n=e.computeEdgeEnds(t.getEdgeIterator());this.insertEdgeEnds(n)},computeIntersectionNodes:function(t,e){for(var n=t.getEdgeIterator();n.hasNext();)for(var i=n.next(),r=i.getLabel().getLocation(e),s=i.getEdgeIntersectionList().iterator();s.hasNext();){var o=s.next(),a=this.nodes.addNode(o.coord);r===L.BOUNDARY?a.setLabelBoundary(e):a.getLabel().isNull(e)&&a.setLabel(e,L.INTERIOR)}},interfaces_:function(){return[]},getClass:function(){return ss}}),e(os.prototype,{isNodeEdgeAreaLabelsConsistent:function(){for(var t=this.nodeGraph.getNodeIterator();t.hasNext();){var e=t.next();if(!e.getEdges().isAreaLabelsConsistent(this.geomGraph))return this.invalidPoint=e.getCoordinate().copy(),!1}return!0},getInvalidPoint:function(){return this.invalidPoint},hasDuplicateRings:function(){for(var t=this.nodeGraph.getNodeIterator();t.hasNext();)for(var e=t.next(),n=e.getEdges().iterator();n.hasNext();){var i=n.next();if(i.getEdgeEnds().size()>1)return this.invalidPoint=i.getEdge().getCoordinate(0),!0}return!1},isNodeConsistentArea:function(){var t=this.geomGraph.computeSelfNodes(this.li,!0,!0);return t.hasProperIntersection()?(this.invalidPoint=t.getProperIntersectionPoint(),!1):(this.nodeGraph.build(this.geomGraph),this.isNodeEdgeAreaLabelsConsistent())},interfaces_:function(){return[]},getClass:function(){return os}}),e(as.prototype,{buildIndex:function(){this.index=new ke;for(var t=0;t<this.rings.size();t++){var e=this.rings.get(t),n=e.getEnvelopeInternal();this.index.insert(n,e)}},getNestedPoint:function(){return this.nestedPt},isNonNested:function(){this.buildIndex();for(var t=0;t<this.rings.size();t++)for(var e=this.rings.get(t),n=e.getCoordinates(),i=this.index.query(e.getEnvelopeInternal()),r=0;r<i.size();r++){var s=i.get(r),o=s.getCoordinates();if(e!==s&&e.getEnvelopeInternal().intersects(s.getEnvelopeInternal())){var a=ls.findPtNotNode(n,s,this.graph);if(null!==a){var u=he.isPointInRing(a,o);if(u)return this.nestedPt=a,!1}}}return!0},add:function(t){this.rings.add(t),this.totalEnv.expandToInclude(t.getEnvelopeInternal())},interfaces_:function(){return[]},getClass:function(){return as}}),e(us.prototype,{getErrorType:function(){return this.errorType},getMessage:function(){return us.errMsg[this.errorType]},getCoordinate:function(){return this.pt},toString:function(){var t="";return null!==this.pt&&(t=" at or near point "+this.pt),this.getMessage()+t},interfaces_:function(){return[]},getClass:function(){return us}}),us.ERROR=0,us.REPEATED_POINT=1,us.HOLE_OUTSIDE_SHELL=2,us.NESTED_HOLES=3,us.DISCONNECTED_INTERIOR=4,us.SELF_INTERSECTION=5,us.RING_SELF_INTERSECTION=6,us.NESTED_SHELLS=7,us.DUPLICATE_RINGS=8,us.TOO_FEW_POINTS=9,us.INVALID_COORDINATE=10,us.RING_NOT_CLOSED=11,us.errMsg=["Topology Validation Error","Repeated Point","Hole lies outside shell","Holes are nested","Interior is disconnected","Self-intersection","Ring Self-intersection","Nested shells","Duplicate Rings","Too few distinct points in geometry component","Invalid Coordinate","Ring is not closed"],e(ls.prototype,{checkInvalidCoordinates:function(){if(arguments[0]instanceof Array){for(var t=arguments[0],e=0;e<t.length;e++)if(!ls.isValid(t[e]))return this.validErr=new us(us.INVALID_COORDINATE,t[e]),null}else if(arguments[0]instanceof Tt){var n=arguments[0];if(this.checkInvalidCoordinates(n.getExteriorRing().getCoordinates()),null!==this.validErr)return null;for(var e=0;e<n.getNumInteriorRing();e++)if(this.checkInvalidCoordinates(n.getInteriorRingN(e).getCoordinates()),null!==this.validErr)return null}},checkHolesNotNested:function(t,e){for(var n=new as(e),i=0;i<t.getNumInteriorRing();i++){var r=t.getInteriorRingN(i);n.add(r)}var s=n.isNonNested();s||(this.validErr=new us(us.NESTED_HOLES,n.getNestedPoint()))},checkConsistentArea:function(t){var e=new os(t),n=e.isNodeConsistentArea();return n?void(e.hasDuplicateRings()&&(this.validErr=new us(us.DUPLICATE_RINGS,e.getInvalidPoint()))):(this.validErr=new us(us.SELF_INTERSECTION,e.getInvalidPoint()),null)},isValid:function(){return this.checkValid(this.parentGeometry),null===this.validErr},checkShellInsideHole:function(t,e,n){var i=t.getCoordinates(),r=e.getCoordinates(),s=ls.findPtNotNode(i,e,n);if(null!==s){var o=he.isPointInRing(s,r);if(!o)return s}var a=ls.findPtNotNode(r,t,n);if(null!==a){var u=he.isPointInRing(a,i);return u?a:null}return f.shouldNeverReachHere("points in shell and hole appear to be equal"),null},checkNoSelfIntersectingRings:function(t){for(var e=t.getEdgeIterator();e.hasNext();){var n=e.next();if(this.checkNoSelfIntersectingRing(n.getEdgeIntersectionList()),null!==this.validErr)return null}},checkConnectedInteriors:function(t){var e=new rs(t);e.isInteriorsConnected()||(this.validErr=new us(us.DISCONNECTED_INTERIOR,e.getCoordinate()))},checkNoSelfIntersectingRing:function(t){for(var e=new at,n=!0,i=t.iterator();i.hasNext();){var r=i.next();if(n)n=!1;else{if(e.contains(r.coord))return this.validErr=new us(us.RING_SELF_INTERSECTION,r.coord),null;e.add(r.coord)}}},checkHolesInShell:function(t,e){for(var n=t.getExteriorRing(),i=new Ii(n),r=0;r<t.getNumInteriorRing();r++){var s=t.getInteriorRingN(r),o=ls.findPtNotNode(s.getCoordinates(),n,e);if(null===o)return null;var a=!i.isInside(o);if(a)return this.validErr=new us(us.HOLE_OUTSIDE_SHELL,o),null}},checkTooFewPoints:function(t){return t.hasTooFewPoints()?(this.validErr=new us(us.TOO_FEW_POINTS,t.getInvalidPoint()),null):void 0},getValidationError:function(){return this.checkValid(this.parentGeometry),this.validErr},checkValid:function(){if(arguments[0]instanceof Lt){var t=arguments[0];this.checkInvalidCoordinates(t.getCoordinates())}else if(arguments[0]instanceof Pt){var e=arguments[0];this.checkInvalidCoordinates(e.getCoordinates())}else if(arguments[0]instanceof bt){var n=arguments[0];if(this.checkInvalidCoordinates(n.getCoordinates()),null!==this.validErr)return null;if(this.checkClosedRing(n),null!==this.validErr)return null;var i=new $n(0,n);if(this.checkTooFewPoints(i),null!==this.validErr)return null;var r=new ae;i.computeSelfNodes(r,!0,!0),this.checkNoSelfIntersectingRings(i)}else if(arguments[0]instanceof St){var s=arguments[0];if(this.checkInvalidCoordinates(s.getCoordinates()),null!==this.validErr)return null;var i=new $n(0,s);this.checkTooFewPoints(i)}else if(arguments[0]instanceof Tt){var o=arguments[0];if(this.checkInvalidCoordinates(o),null!==this.validErr)return null;if(this.checkClosedRings(o),null!==this.validErr)return null;var i=new $n(0,o);if(this.checkTooFewPoints(i),null!==this.validErr)return null;if(this.checkConsistentArea(i),null!==this.validErr)return null;if(!this.isSelfTouchingRingFormingHoleValid&&(this.checkNoSelfIntersectingRings(i),null!==this.validErr))return null;if(this.checkHolesInShell(o,i),null!==this.validErr)return null;if(this.checkHolesNotNested(o,i),null!==this.validErr)return null;this.checkConnectedInteriors(i)}else if(arguments[0]instanceof Ot){for(var a=arguments[0],u=0;u<a.getNumGeometries();u++){var l=a.getGeometryN(u);if(this.checkInvalidCoordinates(l),null!==this.validErr)return null;if(this.checkClosedRings(l),null!==this.validErr)return null}var i=new $n(0,a);if(this.checkTooFewPoints(i),null!==this.validErr)return null;if(this.checkConsistentArea(i),null!==this.validErr)return null;if(!this.isSelfTouchingRingFormingHoleValid&&(this.checkNoSelfIntersectingRings(i),null!==this.validErr))return null;for(var u=0;u<a.getNumGeometries();u++){var l=a.getGeometryN(u);if(this.checkHolesInShell(l,i),null!==this.validErr)return null}for(var u=0;u<a.getNumGeometries();u++){var l=a.getGeometryN(u);if(this.checkHolesNotNested(l,i),null!==this.validErr)return null}if(this.checkShellsNotNested(a,i),null!==this.validErr)return null;this.checkConnectedInteriors(i)}else if(arguments[0]instanceof ft)for(var h=arguments[0],u=0;u<h.getNumGeometries();u++){var c=h.getGeometryN(u);if(this.checkValid(c),null!==this.validErr)return null}else if(arguments[0]instanceof B){var f=arguments[0];if(this.validErr=null,f.isEmpty())return null;if(f instanceof Lt)this.checkValid(f);else if(f instanceof Pt)this.checkValid(f);else if(f instanceof bt)this.checkValid(f);else if(f instanceof St)this.checkValid(f);else if(f instanceof Tt)this.checkValid(f);else if(f instanceof Ot)this.checkValid(f);else{if(!(f instanceof ft))throw new UnsupportedOperationException(f.getClass().getName());this.checkValid(f)}}},setSelfTouchingRingFormingHoleValid:function(t){this.isSelfTouchingRingFormingHoleValid=t},checkShellNotNested:function(t,e,n){var i=t.getCoordinates(),r=e.getExteriorRing(),s=r.getCoordinates(),o=ls.findPtNotNode(i,r,n);if(null===o)return null;var a=he.isPointInRing(o,s);if(!a)return null;if(e.getNumInteriorRing()<=0)return this.validErr=new us(us.NESTED_SHELLS,o),null;for(var u=null,l=0;l<e.getNumInteriorRing();l++){var h=e.getInteriorRingN(l);if(u=this.checkShellInsideHole(t,h,n),null===u)return null}this.validErr=new us(us.NESTED_SHELLS,u)},checkClosedRings:function(t){if(this.checkClosedRing(t.getExteriorRing()),null!==this.validErr)return null;for(var e=0;e<t.getNumInteriorRing();e++)if(this.checkClosedRing(t.getInteriorRingN(e)),null!==this.validErr)return null},checkClosedRing:function(t){if(!t.isClosed()){var e=null;t.getNumPoints()>=1&&(e=t.getCoordinateN(0)),this.validErr=new us(us.RING_NOT_CLOSED,e)}},checkShellsNotNested:function(t,e){for(var n=0;n<t.getNumGeometries();n++)for(var i=t.getGeometryN(n),r=i.getExteriorRing(),s=0;s<t.getNumGeometries();s++)if(n!==s){var o=t.getGeometryN(s);if(this.checkShellNotNested(r,o,e),null!==this.validErr)return null}},interfaces_:function(){return[]},getClass:function(){return ls}}),ls.findPtNotNode=function(t,e,n){for(var i=n.findEdge(e),r=i.getEdgeIntersectionList(),s=0;s<t.length;s++){var o=t[s];if(!r.isIntersection(o))return o}return null},ls.isValid=function(){if(arguments[0]instanceof B){var t=arguments[0],e=new ls(t);return e.isValid()}if(arguments[0]instanceof g){var n=arguments[0];return r.isNaN(n.x)?!1:r.isInfinite(n.x)?!1:r.isNaN(n.y)?!1:!r.isInfinite(n.y)}};var Oo=Object.freeze({IsValidOp:ls,ConsistentAreaTester:os}),_o=Object.freeze({BoundaryOp:dt,IsSimpleOp:Wi,buffer:So,distance:wo,linemerge:Lo,overlay:Ro,polygonize:To,relate:Po,union:bo,valid:Oo});h(hs,_t.CoordinateOperation),e(hs.prototype,{editCoordinates:function(t,e){if(0===t.length)return null;for(var n=new Array(t.length).fill(null),i=0;i<t.length;i++){var r=new g(t[i]);this.targetPM.makePrecise(r),n[i]=r}var s=new N(n,!1),o=s.toCoordinateArray(),a=0;e instanceof St&&(a=2),e instanceof bt&&(a=4);var u=n;return this.removeCollapsed&&(u=null),o.length<a?u:o},interfaces_:function(){return[]},getClass:function(){return hs}}),e(cs.prototype,{fixPolygonalTopology:function(t){var e=t;this.changePrecisionModel||(e=this.changePM(t,this.targetPM));var n=e.buffer(0),i=n;return this.changePrecisionModel||(i=t.getFactory().createGeometry(n)),i},reducePointwise:function(t){var e=null;if(this.changePrecisionModel){var n=this.createFactory(t.getFactory(),this.targetPM);e=new _t(n)}else e=new _t;var i=this.removeCollapsed;t.getDimension()>=2&&(i=!0);var r=e.edit(t,new hs(this.targetPM,i));return r},changePM:function(t,e){var n=this.createEditor(t.getFactory(),e);return n.edit(t,new _t.NoOpGeometryOperation)},setRemoveCollapsedComponents:function(t){this.removeCollapsed=t},createFactory:function(t,e){var n=new ie(e,t.getSRID(),t.getCoordinateSequenceFactory());return n},setChangePrecisionModel:function(t){this.changePrecisionModel=t},reduce:function(t){var e=this.reducePointwise(t);return this.isPointwise?e:R(e,Rt)?e.isValid()?e:this.fixPolygonalTopology(e):e},setPointwise:function(t){this.isPointwise=t},createEditor:function(t,e){if(t.getPrecisionModel()===e)return new _t;var n=this.createFactory(t,e),i=new _t(n);return i},interfaces_:function(){return[]},getClass:function(){return cs}}),cs.reduce=function(t,e){var n=new cs(e);return n.reduce(t)},cs.reducePointwise=function(t,e){var n=new cs(e);return n.setPointwise(!0),n.reduce(t)};var Mo=Object.freeze({GeometryPrecisionReducer:cs});e(fs.prototype,{simplifySection:function(t,e){if(t+1===e)return null;this.seg.p0=this.pts[t],this.seg.p1=this.pts[e];for(var n=-1,i=t,r=t+1;e>r;r++){var s=this.seg.distance(this.pts[r]);s>n&&(n=s,i=r)}if(n<=this.distanceTolerance)for(var r=t+1;e>r;r++)this.usePt[r]=!1;else this.simplifySection(t,i),this.simplifySection(i,e)},setDistanceTolerance:function(t){this.distanceTolerance=t},simplify:function(){this.usePt=new Array(this.pts.length).fill(null);for(var t=0;t<this.pts.length;t++)this.usePt[t]=!0;this.simplifySection(0,this.pts.length-1);for(var e=new N,t=0;t<this.pts.length;t++)this.usePt[t]&&e.add(new g(this.pts[t]));return e.toCoordinateArray()},interfaces_:function(){return[]},getClass:function(){return fs}}),fs.simplify=function(t,e){var n=new fs(t);return n.setDistanceTolerance(e),n.simplify()},e(gs.prototype,{setEnsureValid:function(t){this.isEnsureValidTopology=t},getResultGeometry:function(){return this.inputGeom.isEmpty()?this.inputGeom.copy():new ds(this.isEnsureValidTopology,this.distanceTolerance).transform(this.inputGeom)},setDistanceTolerance:function(t){if(0>t)throw new i("Tolerance must be non-negative");this.distanceTolerance=t},interfaces_:function(){return[]},getClass:function(){return gs}}),gs.simplify=function(t,e){var n=new gs(t);return n.setDistanceTolerance(e),n.getResultGeometry()},h(ds,xe),e(ds.prototype,{transformPolygon:function(t,e){if(t.isEmpty())return null;var n=xe.prototype.transformPolygon.call(this,t,e);return e instanceof Ot?n:this.createValidArea(n)},createValidArea:function(t){return this.isEnsureValidTopology?t.buffer(0):t},transformCoordinates:function(t,e){var n=t.toCoordinateArray(),i=null;return i=0===n.length?new Array(0).fill(null):fs.simplify(n,this.distanceTolerance),this.factory.getCoordinateSequenceFactory().create(i)},transformMultiPolygon:function(t,e){var n=xe.prototype.transformMultiPolygon.call(this,t,e);return this.createValidArea(n)},transformLinearRing:function(t,e){var n=e instanceof Tt,i=xe.prototype.transformLinearRing.call(this,t,e);return!n||i instanceof bt?i:null},interfaces_:function(){return[]},getClass:function(){return ds}}),gs.DPTransformer=ds,h(ps,ce),e(ps.prototype,{getIndex:function(){return this.index},getParent:function(){return this.parent},interfaces_:function(){return[]},getClass:function(){return ps}}),e(vs.prototype,{addToResult:function(t){this.resultSegs.add(t)},asLineString:function(){return this.parentLine.getFactory().createLineString(vs.extractCoordinates(this.resultSegs))},getResultSize:function(){var t=this.resultSegs.size();return 0===t?0:t+1},getParent:function(){return this.parentLine},getSegment:function(t){return this.segs[t]},getParentCoordinates:function(){return this.parentLine.getCoordinates()},getMinimumSize:function(){return this.minimumSize},asLinearRing:function(){return this.parentLine.getFactory().createLinearRing(vs.extractCoordinates(this.resultSegs))},getSegments:function(){return this.segs},init:function(){var t=this.parentLine.getCoordinates();this.segs=new Array(t.length-1).fill(null);for(var e=0;e<t.length-1;e++){var n=new ps(t[e],t[e+1],this.parentLine,e);this.segs[e]=n}},getResultCoordinates:function(){return vs.extractCoordinates(this.resultSegs)},interfaces_:function(){return[]},getClass:function(){return vs}}),vs.extractCoordinates=function(t){for(var e=new Array(t.size()+1).fill(null),n=null,i=0;i<t.size();i++)n=t.get(i),e[i]=n.p0;return e[e.length-1]=n.p1,e},e(ms.prototype,{remove:function(t){this.index.remove(new C(t.p0,t.p1),t)},add:function(){if(arguments[0]instanceof vs)for(var t=arguments[0],e=t.getSegments(),n=0;n<e.length;n++){var i=e[n];this.add(i)}else if(arguments[0]instanceof ce){var r=arguments[0];this.index.insert(new C(r.p0,r.p1),r)}},query:function(t){var e=new C(t.p0,t.p1),n=new ys(t);this.index.query(e,n);var i=n.getItems();return i},interfaces_:function(){return[]},getClass:function(){return ms}}),e(ys.prototype,{visitItem:function(t){var e=t;C.intersects(e.p0,e.p1,this.querySeg.p0,this.querySeg.p1)&&this.items.add(t)},getItems:function(){return this.items},interfaces_:function(){return[Ae]},getClass:function(){return ys}}),e(xs.prototype,{flatten:function(t,e){var n=this.linePts[t],i=this.linePts[e],r=new ce(n,i);return this.remove(this.line,t,e),this.outputIndex.add(r),r},hasBadIntersection:function(t,e,n){return this.hasBadOutputIntersection(n)?!0:!!this.hasBadInputIntersection(t,e,n)},setDistanceTolerance:function(t){this.distanceTolerance=t},simplifySection:function(t,e,n){n+=1;var i=new Array(2).fill(null);if(t+1===e){var r=this.line.getSegment(t);return this.line.addToResult(r),null}var s=!0;if(this.line.getResultSize()<this.line.getMinimumSize()){var o=n+1;o<this.line.getMinimumSize()&&(s=!1)}var a=new Array(1).fill(null),u=this.findFurthestPoint(this.linePts,t,e,a);a[0]>this.distanceTolerance&&(s=!1);var l=new ce;if(l.p0=this.linePts[t],l.p1=this.linePts[e],i[0]=t,i[1]=e,this.hasBadIntersection(this.line,i,l)&&(s=!1),s){var r=this.flatten(t,e);return this.line.addToResult(r),null}this.simplifySection(t,u,n),this.simplifySection(u,e,n)},hasBadOutputIntersection:function(t){for(var e=this.outputIndex.query(t),n=e.iterator();n.hasNext();){var i=n.next();if(this.hasInteriorIntersection(i,t))return!0}return!1},findFurthestPoint:function(t,e,n,i){var r=new ce;r.p0=t[e],r.p1=t[n];for(var s=-1,o=e,a=e+1;n>a;a++){var u=t[a],l=r.distance(u);l>s&&(s=l,o=a)}return i[0]=s,o},simplify:function(t){this.line=t,this.linePts=t.getParentCoordinates(),this.simplifySection(0,this.linePts.length-1,0)},remove:function(t,e,n){for(var i=e;n>i;i++){var r=t.getSegment(i);this.inputIndex.remove(r)}},hasInteriorIntersection:function(t,e){return this.li.computeIntersection(t.p0,t.p1,e.p0,e.p1),this.li.isInteriorIntersection()},hasBadInputIntersection:function(t,e,n){for(var i=this.inputIndex.query(n),r=i.iterator();r.hasNext();){var s=r.next();if(this.hasInteriorIntersection(s,n)){if(xs.isInLineSection(t,e,s))continue;return!0}}return!1},interfaces_:function(){return[]},getClass:function(){return xs}}),xs.isInLineSection=function(t,e,n){if(n.getParent()!==t.getParent())return!1;var i=n.getIndex();return i>=e[0]&&i<e[1]},e(Es.prototype,{setDistanceTolerance:function(t){this.distanceTolerance=t},simplify:function(t){for(var e=t.iterator();e.hasNext();)this.inputIndex.add(e.next());for(var e=t.iterator();e.hasNext();){var n=new xs(this.inputIndex,this.outputIndex);n.setDistanceTolerance(this.distanceTolerance),n.simplify(e.next())}},interfaces_:function(){return[]},getClass:function(){return Es}}),e(Is.prototype,{getResultGeometry:function(){if(this.inputGeom.isEmpty())return this.inputGeom.copy();this.linestringMap=new te,this.inputGeom.apply(new Cs(this)),this.lineSimplifier.simplify(this.linestringMap.values());var t=new Ns(this.linestringMap).transform(this.inputGeom);return t},setDistanceTolerance:function(t){if(0>t)throw new i("Tolerance must be non-negative");this.lineSimplifier.setDistanceTolerance(t)},interfaces_:function(){return[]},getClass:function(){return Is}}),Is.simplify=function(t,e){var n=new Is(t);return n.setDistanceTolerance(e),n.getResultGeometry()},h(Ns,xe),e(Ns.prototype,{transformCoordinates:function(t,e){if(0===t.size())return null;if(e instanceof St){var n=this.linestringMap.get(e);return this.createCoordinateSequence(n.getResultCoordinates())}return xe.prototype.transformCoordinates.call(this,t,e)},interfaces_:function(){return[]},getClass:function(){return Ns}}),e(Cs.prototype,{filter:function(t){if(t instanceof St){var e=t;if(e.isEmpty())return null;var n=e.isClosed()?4:2,i=new vs(e,n);this.tps.linestringMap.put(e,i)}},interfaces_:function(){return[q]},getClass:function(){return Cs}}),Is.LineStringTransformer=Ns,Is.LineStringMapBuilderFilter=Cs;var Do=Object.freeze({DouglasPeuckerSimplifier:gs,TopologyPreservingSimplifier:Is});e(Ss.prototype,{splitAt:function(){if(1===arguments.length){var t=arguments[0],e=this.minimumLen/this.segLen;if(t.distance(this.seg.p0)<this.minimumLen)return this.splitPt=this.seg.pointAlong(e),null;if(t.distance(this.seg.p1)<this.minimumLen)return this.splitPt=Ss.pointAlongReverse(this.seg,e),null;this.splitPt=t}else if(2===arguments.length){var n=arguments[0],i=arguments[1],r=this.getConstrainedLength(n),s=r/this.segLen;i.equals2D(this.seg.p0)?this.splitPt=this.seg.pointAlong(s):this.splitPt=Ss.pointAlongReverse(this.seg,s)}},setMinimumLength:function(t){this.minimumLen=t},getConstrainedLength:function(t){return t<this.minimumLen?this.minimumLen:t},getSplitPoint:function(){return this.splitPt},interfaces_:function(){return[]},getClass:function(){return Ss}}),Ss.pointAlongReverse=function(t,e){var n=new g;return n.x=t.p1.x-e*(t.p1.x-t.p0.x),n.y=t.p1.y-e*(t.p1.y-t.p0.y),n},e(ws.prototype,{findSplitPoint:function(t,e){},interfaces_:function(){return[]},getClass:function(){return ws}}),e(Ls.prototype,{findSplitPoint:function(t,e){var n=t.getLineSegment(),i=n.getLength(),r=i/2,s=new Ss(n),o=Ls.projectedSplitPoint(t,e),a=2*o.distance(e)*.8,u=a;return u>r&&(u=r),s.setMinimumLength(u),s.splitAt(o),s.getSplitPoint()},interfaces_:function(){return[ws]},getClass:function(){return Ls}}),Ls.projectedSplitPoint=function(t,e){var n=t.getLineSegment(),i=n.project(e);return i},e(Rs.prototype,{interfaces_:function(){return[]},getClass:function(){return Rs}}),Rs.triArea=function(t,e,n){return(e.x-t.x)*(n.y-t.y)-(e.y-t.y)*(n.x-t.x)},Rs.isInCircleDDNormalized=function(t,e,n,i){var r=_.valueOf(t.x).selfSubtract(i.x),s=_.valueOf(t.y).selfSubtract(i.y),o=_.valueOf(e.x).selfSubtract(i.x),a=_.valueOf(e.y).selfSubtract(i.y),u=_.valueOf(n.x).selfSubtract(i.x),l=_.valueOf(n.y).selfSubtract(i.y),h=r.multiply(a).selfSubtract(o.multiply(s)),c=o.multiply(l).selfSubtract(u.multiply(a)),f=u.multiply(s).selfSubtract(r.multiply(l)),g=r.multiply(r).selfAdd(s.multiply(s)),d=o.multiply(o).selfAdd(a.multiply(a)),p=u.multiply(u).selfAdd(l.multiply(l)),v=g.selfMultiply(c).selfAdd(d.selfMultiply(f)).selfAdd(p.selfMultiply(h)),m=v.doubleValue()>0;return m},Rs.checkRobustInCircle=function(t,e,n,i){var r=Rs.isInCircleNonRobust(t,e,n,i),s=Rs.isInCircleDDSlow(t,e,n,i),o=Rs.isInCircleCC(t,e,n,i),a=Si.circumcentre(t,e,n);A.out.println("p radius diff a = "+Math.abs(i.distance(a)-t.distance(a))/t.distance(a)),r===s&&r===o||(A.out.println("inCircle robustness failure (double result = "+r+", DD result = "+s+", CC result = "+o+")"),A.out.println(se.toLineString(new Gt([t,e,n,i]))),A.out.println("Circumcentre = "+se.toPoint(a)+" radius = "+t.distance(a)),A.out.println("p radius diff a = "+Math.abs(i.distance(a)/t.distance(a)-1)),A.out.println("p radius diff b = "+Math.abs(i.distance(a)/e.distance(a)-1)),A.out.println("p radius diff c = "+Math.abs(i.distance(a)/n.distance(a)-1)),A.out.println())},Rs.isInCircleDDFast=function(t,e,n,i){var r=_.sqr(t.x).selfAdd(_.sqr(t.y)).selfMultiply(Rs.triAreaDDFast(e,n,i)),s=_.sqr(e.x).selfAdd(_.sqr(e.y)).selfMultiply(Rs.triAreaDDFast(t,n,i)),o=_.sqr(n.x).selfAdd(_.sqr(n.y)).selfMultiply(Rs.triAreaDDFast(t,e,i)),a=_.sqr(i.x).selfAdd(_.sqr(i.y)).selfMultiply(Rs.triAreaDDFast(t,e,n)),u=r.selfSubtract(s).selfAdd(o).selfSubtract(a),l=u.doubleValue()>0;return l},Rs.isInCircleCC=function(t,e,n,i){var r=Si.circumcentre(t,e,n),s=t.distance(r),o=i.distance(r)-s;return 0>=o},Rs.isInCircleNormalized=function(t,e,n,i){var r=t.x-i.x,s=t.y-i.y,o=e.x-i.x,a=e.y-i.y,u=n.x-i.x,l=n.y-i.y,h=r*a-o*s,c=o*l-u*a,f=u*s-r*l,g=r*r+s*s,d=o*o+a*a,p=u*u+l*l,v=g*c+d*f+p*h;return v>0},Rs.isInCircleDDSlow=function(t,e,n,i){var r=_.valueOf(i.x),s=_.valueOf(i.y),o=_.valueOf(t.x),a=_.valueOf(t.y),u=_.valueOf(e.x),l=_.valueOf(e.y),h=_.valueOf(n.x),c=_.valueOf(n.y),f=o.multiply(o).add(a.multiply(a)).multiply(Rs.triAreaDDSlow(u,l,h,c,r,s)),g=u.multiply(u).add(l.multiply(l)).multiply(Rs.triAreaDDSlow(o,a,h,c,r,s)),d=h.multiply(h).add(c.multiply(c)).multiply(Rs.triAreaDDSlow(o,a,u,l,r,s)),p=r.multiply(r).add(s.multiply(s)).multiply(Rs.triAreaDDSlow(o,a,u,l,h,c)),v=f.subtract(g).add(d).subtract(p),m=v.doubleValue()>0;
return m},Rs.isInCircleNonRobust=function(t,e,n,i){var r=(t.x*t.x+t.y*t.y)*Rs.triArea(e,n,i)-(e.x*e.x+e.y*e.y)*Rs.triArea(t,n,i)+(n.x*n.x+n.y*n.y)*Rs.triArea(t,e,i)-(i.x*i.x+i.y*i.y)*Rs.triArea(t,e,n)>0;return r},Rs.isInCircleRobust=function(t,e,n,i){return Rs.isInCircleNormalized(t,e,n,i)},Rs.triAreaDDSlow=function(t,e,n,i,r,s){return n.subtract(t).multiply(s.subtract(e)).subtract(i.subtract(e).multiply(r.subtract(t)))},Rs.triAreaDDFast=function(t,e,n){var i=_.valueOf(e.x).selfSubtract(t.x).selfMultiply(_.valueOf(n.y).selfSubtract(t.y)),r=_.valueOf(e.y).selfSubtract(t.y).selfMultiply(_.valueOf(n.x).selfSubtract(t.x));return i.selfSubtract(r)},e(Ts.prototype,{circleCenter:function(t,e){var n=new Ts(this.getX(),this.getY()),i=this.bisector(n,t),r=this.bisector(t,e),s=new F(i,r),o=null;try{o=new Ts(s.getX(),s.getY())}catch(a){if(!(a instanceof w))throw a;A.err.println("a: "+n+"  b: "+t+"  c: "+e),A.err.println(a)}finally{}return o},dot:function(t){return this.p.x*t.getX()+this.p.y*t.getY()},magn:function(){return Math.sqrt(this.p.x*this.p.x+this.p.y*this.p.y)},getZ:function(){return this.p.z},bisector:function(t,e){var n=e.getX()-t.getX(),i=e.getY()-t.getY(),r=new F(t.getX()+n/2,t.getY()+i/2,1),s=new F(t.getX()-i+n/2,t.getY()+n+i/2,1);return new F(r,s)},equals:function(){if(1===arguments.length){var t=arguments[0];return this.p.x===t.getX()&&this.p.y===t.getY()}if(2===arguments.length){var e=arguments[0],n=arguments[1];return this.p.distance(e.getCoordinate())<n}},getCoordinate:function(){return this.p},isInCircle:function(t,e,n){return Rs.isInCircleRobust(t.p,e.p,n.p,this.p)},interpolateZValue:function(t,e,n){var i=t.getX(),r=t.getY(),s=e.getX()-i,o=n.getX()-i,a=e.getY()-r,u=n.getY()-r,l=s*u-o*a,h=this.getX()-i,c=this.getY()-r,f=(u*h-o*c)/l,g=(-a*h+s*c)/l,d=t.getZ()+f*(e.getZ()-t.getZ())+g*(n.getZ()-t.getZ());return d},midPoint:function(t){var e=(this.p.x+t.getX())/2,n=(this.p.y+t.getY())/2,i=(this.p.z+t.getZ())/2;return new Ts(e,n,i)},rightOf:function(t){return this.isCCW(t.dest(),t.orig())},isCCW:function(t,e){return(t.p.x-this.p.x)*(e.p.y-this.p.y)-(t.p.y-this.p.y)*(e.p.x-this.p.x)>0},getX:function(){return this.p.x},crossProduct:function(t){return this.p.x*t.getY()-this.p.y*t.getX()},setZ:function(t){this.p.z=t},times:function(t){return new Ts(t*this.p.x,t*this.p.y)},cross:function(){return new Ts(this.p.y,-this.p.x)},leftOf:function(t){return this.isCCW(t.orig(),t.dest())},toString:function(){return"POINT ("+this.p.x+" "+this.p.y+")"},sub:function(t){return new Ts(this.p.x-t.getX(),this.p.y-t.getY())},getY:function(){return this.p.y},classify:function(t,e){var n=this,i=e.sub(t),r=n.sub(t),s=i.crossProduct(r);return s>0?Ts.LEFT:0>s?Ts.RIGHT:i.getX()*r.getX()<0||i.getY()*r.getY()<0?Ts.BEHIND:i.magn()<r.magn()?Ts.BEYOND:t.equals(n)?Ts.ORIGIN:e.equals(n)?Ts.DESTINATION:Ts.BETWEEN},sum:function(t){return new Ts(this.p.x+t.getX(),this.p.y+t.getY())},distance:function(t,e){return Math.sqrt(Math.pow(e.getX()-t.getX(),2)+Math.pow(e.getY()-t.getY(),2))},circumRadiusRatio:function(t,e){var n=this.circleCenter(t,e),i=this.distance(n,t),r=this.distance(this,t),s=this.distance(t,e);return r>s&&(r=s),s=this.distance(e,this),r>s&&(r=s),i/r},interfaces_:function(){return[]},getClass:function(){return Ts}}),Ts.interpolateZ=function(){if(3===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],i=e.distance(n),r=t.distance(e),s=n.z-e.z,o=e.z+s*(r/i);return o}if(4===arguments.length){var a=arguments[0],u=arguments[1],l=arguments[2],h=arguments[3],c=u.x,f=u.y,g=l.x-c,d=h.x-c,p=l.y-f,v=h.y-f,m=g*v-d*p,y=a.x-c,x=a.y-f,E=(v*y-d*x)/m,I=(-p*y+g*x)/m,N=u.z+E*(l.z-u.z)+I*(h.z-u.z);return N}},Ts.LEFT=0,Ts.RIGHT=1,Ts.BEYOND=2,Ts.BEHIND=3,Ts.BETWEEN=4,Ts.ORIGIN=5,Ts.DESTINATION=6,h(Ps,Ts),e(Ps.prototype,{getConstraint:function(){return this.constraint},setOnConstraint:function(t){this._isOnConstraint=t},merge:function(t){t._isOnConstraint&&(this._isOnConstraint=!0,this.constraint=t.constraint)},isOnConstraint:function(){return this._isOnConstraint},setConstraint:function(t){this._isOnConstraint=!0,this.constraint=t},interfaces_:function(){return[]},getClass:function(){return Ps}}),e(bs.prototype,{equalsNonOriented:function(t){return this.equalsOriented(t)?!0:!!this.equalsOriented(t.sym())},toLineSegment:function(){return new ce(this.vertex.getCoordinate(),this.dest().getCoordinate())},dest:function(){return this.sym().orig()},oNext:function(){return this.next},equalsOriented:function(t){return!(!this.orig().getCoordinate().equals2D(t.orig().getCoordinate())||!this.dest().getCoordinate().equals2D(t.dest().getCoordinate()))},dNext:function(){return this.sym().oNext().sym()},lPrev:function(){return this.next.sym()},rPrev:function(){return this.sym().oNext()},rot:function(){return this._rot},oPrev:function(){return this._rot.next._rot},sym:function(){return this._rot._rot},setOrig:function(t){this.vertex=t},lNext:function(){return this.invRot().oNext().rot()},getLength:function(){return this.orig().getCoordinate().distance(this.dest().getCoordinate())},invRot:function(){return this._rot.sym()},setDest:function(t){this.sym().setOrig(t)},setData:function(t){this.data=t},getData:function(){return this.data},"delete":function(){this._rot=null},orig:function(){return this.vertex},rNext:function(){return this._rot.next.invRot()},toString:function(){var t=this.vertex.getCoordinate(),e=this.dest().getCoordinate();return se.toLineString(t,e)},isLive:function(){return null!==this._rot},getPrimary:function(){return this.orig().getCoordinate().compareTo(this.dest().getCoordinate())<=0?this:this.sym()},dPrev:function(){return this.invRot().oNext().invRot()},setNext:function(t){this.next=t},interfaces_:function(){return[]},getClass:function(){return bs}}),bs.makeEdge=function(t,e){var n=new bs,i=new bs,r=new bs,s=new bs;n._rot=i,i._rot=r,r._rot=s,s._rot=n,n.setNext(n),i.setNext(s),r.setNext(r),s.setNext(i);var o=n;return o.setOrig(t),o.setDest(e),o},bs.swap=function(t){var e=t.oPrev(),n=t.sym().oPrev();bs.splice(t,e),bs.splice(t.sym(),n),bs.splice(t,e.lNext()),bs.splice(t.sym(),n.lNext()),t.setOrig(e.dest()),t.setDest(n.dest())},bs.splice=function(t,e){var n=t.oNext().rot(),i=e.oNext().rot(),r=e.oNext(),s=t.oNext(),o=i.oNext(),a=n.oNext();t.setNext(r),e.setNext(s),n.setNext(o),i.setNext(a)},bs.connect=function(t,e){var n=bs.makeEdge(t.dest(),e.orig());return bs.splice(n,t.lNext()),bs.splice(n.sym(),e),n},e(Os.prototype,{insertSite:function(t){var e=this.subdiv.locate(t);if(this.subdiv.isVertexOfEdge(e,t))return e;this.subdiv.isOnEdge(e,t.getCoordinate())&&(e=e.oPrev(),this.subdiv["delete"](e.oNext()));var n=this.subdiv.makeEdge(e.orig(),t);bs.splice(n,e);var i=n;do n=this.subdiv.connect(e,n.sym()),e=n.oPrev();while(e.lNext()!==i);for(;;){var r=e.oPrev();if(r.dest().rightOf(e)&&t.isInCircle(e.orig(),r.dest(),e.dest()))bs.swap(e),e=e.oPrev();else{if(e.oNext()===i)return n;e=e.oNext().lPrev()}}},insertSites:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.insertSite(n)}},interfaces_:function(){return[]},getClass:function(){return Os}}),e(_s.prototype,{locate:function(t){},interfaces_:function(){return[]},getClass:function(){return _s}}),e(Ms.prototype,{init:function(){this.lastEdge=this.findEdge()},locate:function(t){this.lastEdge.isLive()||this.init();var e=this.subdiv.locateFromEdge(t,this.lastEdge);return this.lastEdge=e,e},findEdge:function(){var t=this.subdiv.getEdges();return t.iterator().next()},interfaces_:function(){return[_s]},getClass:function(){return Ms}}),h(Ds,l),e(Ds.prototype,{getSegment:function(){return this.seg},interfaces_:function(){return[]},getClass:function(){return Ds}}),Ds.msgWithSpatial=function(t,e){return null!==e?t+" [ "+e+" ]":t},e(As.prototype,{visit:function(t){},interfaces_:function(){return[]},getClass:function(){return As}}),e(Fs.prototype,{getTriangleVertices:function(t){var e=new Bs;return this.visitTriangles(e,t),e.getTriangleVertices()},isFrameVertex:function(t){return t.equals(this.frameVertex[0])?!0:t.equals(this.frameVertex[1])?!0:!!t.equals(this.frameVertex[2])},isVertexOfEdge:function(t,e){return!(!e.equals(t.orig(),this.tolerance)&&!e.equals(t.dest(),this.tolerance))},connect:function(t,e){var n=bs.connect(t,e);return this.quadEdges.add(n),n},getVoronoiCellPolygon:function(t,e){var n=new I,i=t;do{var r=t.rot().orig().getCoordinate();n.add(r),t=t.oPrev()}while(t!==i);var s=new N;s.addAll(n,!1),s.closeRing(),s.size()<4&&(A.out.println(s),s.add(s.get(s.size()-1),!0));var o=s.toCoordinateArray(),a=e.createPolygon(e.createLinearRing(o),null),u=i.orig();return a.setUserData(u.getCoordinate()),a},setLocator:function(t){this.locator=t},initSubdiv:function(){var t=this.makeEdge(this.frameVertex[0],this.frameVertex[1]),e=this.makeEdge(this.frameVertex[1],this.frameVertex[2]);bs.splice(t.sym(),e);var n=this.makeEdge(this.frameVertex[2],this.frameVertex[0]);return bs.splice(e.sym(),n),bs.splice(n.sym(),t),t},isFrameBorderEdge:function(t){var e=new Array(3).fill(null);Fs.getTriangleEdges(t,e);var n=new Array(3).fill(null);Fs.getTriangleEdges(t.sym(),n);var i=t.lNext().dest();if(this.isFrameVertex(i))return!0;var r=t.sym().lNext().dest();return!!this.isFrameVertex(r)},makeEdge:function(t,e){var n=bs.makeEdge(t,e);return this.quadEdges.add(n),n},visitTriangles:function(t,e){this.visitedKey++;var n=new pe;n.push(this.startingEdge);for(var i=new J;!n.empty();){var r=n.pop();if(!i.contains(r)){var s=this.fetchTriangleToVisit(r,n,e,i);null!==s&&t.visit(s)}}},isFrameEdge:function(t){return!(!this.isFrameVertex(t.orig())&&!this.isFrameVertex(t.dest()))},isOnEdge:function(t,e){this.seg.setCoordinates(t.orig().getCoordinate(),t.dest().getCoordinate());var n=this.seg.distance(e);return n<this.edgeCoincidenceTolerance},getEnvelope:function(){return new C(this.frameEnv)},createFrame:function(t){var e=t.getWidth(),n=t.getHeight(),i=0;i=e>n?10*e:10*n,this.frameVertex[0]=new Ts((t.getMaxX()+t.getMinX())/2,t.getMaxY()+i),this.frameVertex[1]=new Ts(t.getMinX()-i,t.getMinY()-i),this.frameVertex[2]=new Ts(t.getMaxX()+i,t.getMinY()-i),this.frameEnv=new C(this.frameVertex[0].getCoordinate(),this.frameVertex[1].getCoordinate()),this.frameEnv.expandToInclude(this.frameVertex[2].getCoordinate())},getTriangleCoordinates:function(t){var e=new zs;return this.visitTriangles(e,t),e.getTriangles()},getVertices:function(t){for(var e=new J,n=this.quadEdges.iterator();n.hasNext();){var i=n.next(),r=i.orig();!t&&this.isFrameVertex(r)||e.add(r);var s=i.dest();!t&&this.isFrameVertex(s)||e.add(s)}return e},fetchTriangleToVisit:function(t,e,n,i){var r=t,s=0,o=!1;do{this.triEdges[s]=r,this.isFrameEdge(r)&&(o=!0);var a=r.sym();i.contains(a)||e.push(a),i.add(r),s++,r=r.lNext()}while(r!==t);return o&&!n?null:this.triEdges},getEdges:function(){if(0===arguments.length)return this.quadEdges;if(1===arguments.length){for(var t=arguments[0],e=this.getPrimaryEdges(!1),n=new Array(e.size()).fill(null),i=0,r=e.iterator();r.hasNext();){var s=r.next();n[i++]=t.createLineString([s.orig().getCoordinate(),s.dest().getCoordinate()])}return t.createMultiLineString(n)}},getVertexUniqueEdges:function(t){for(var e=new I,n=new J,i=this.quadEdges.iterator();i.hasNext();){var r=i.next(),s=r.orig();n.contains(s)||(n.add(s),!t&&this.isFrameVertex(s)||e.add(r));var o=r.sym(),a=o.orig();n.contains(a)||(n.add(a),!t&&this.isFrameVertex(a)||e.add(o))}return e},getTriangleEdges:function(t){var e=new qs;return this.visitTriangles(e,t),e.getTriangleEdges()},getPrimaryEdges:function(t){this.visitedKey++;var e=new I,n=new pe;n.push(this.startingEdge);for(var i=new J;!n.empty();){var r=n.pop();if(!i.contains(r)){var s=r.getPrimary();!t&&this.isFrameEdge(s)||e.add(s),n.push(r.oNext()),n.push(r.sym().oNext()),i.add(r),i.add(r.sym())}}return e},"delete":function(t){bs.splice(t,t.oPrev()),bs.splice(t.sym(),t.sym().oPrev());var e=t.sym(),n=t.rot(),i=t.rot().sym();this.quadEdges.remove(t),this.quadEdges.remove(e),this.quadEdges.remove(n),this.quadEdges.remove(i),t["delete"](),e["delete"](),n["delete"](),i["delete"]()},locateFromEdge:function(t,e){for(var n=0,i=this.quadEdges.size(),r=e;;){if(n++,n>i)throw new Ds(r.toLineSegment());if(t.equals(r.orig())||t.equals(r.dest()))break;if(t.rightOf(r))r=r.sym();else if(t.rightOf(r.oNext())){if(t.rightOf(r.dPrev()))break;r=r.dPrev()}else r=r.oNext()}return r},getTolerance:function(){return this.tolerance},getVoronoiCellPolygons:function(t){this.visitTriangles(new Gs,!0);for(var e=new I,n=this.getVertexUniqueEdges(!1),i=n.iterator();i.hasNext();){var r=i.next();e.add(this.getVoronoiCellPolygon(r,t))}return e},getVoronoiDiagram:function(t){var e=this.getVoronoiCellPolygons(t);return t.createGeometryCollection(ie.toGeometryArray(e))},getTriangles:function(t){for(var e=this.getTriangleCoordinates(!1),n=new Array(e.size()).fill(null),i=0,r=e.iterator();r.hasNext();){var s=r.next();n[i++]=t.createPolygon(t.createLinearRing(s),null)}return t.createGeometryCollection(n)},insertSite:function(t){var e=this.locate(t);if(t.equals(e.orig(),this.tolerance)||t.equals(e.dest(),this.tolerance))return e;var n=this.makeEdge(e.orig(),t);bs.splice(n,e);var i=n;do n=this.connect(e,n.sym()),e=n.oPrev();while(e.lNext()!==i);return i},locate:function(){if(1===arguments.length){if(arguments[0]instanceof Ts){var t=arguments[0];return this.locator.locate(t)}if(arguments[0]instanceof g){var e=arguments[0];return this.locator.locate(new Ts(e))}}else if(2===arguments.length){var n=arguments[0],i=arguments[1],r=this.locator.locate(new Ts(n));if(null===r)return null;var s=r;r.dest().getCoordinate().equals2D(n)&&(s=r.sym());var o=s;do{if(o.dest().getCoordinate().equals2D(i))return o;o=o.oNext()}while(o!==s);return null}},interfaces_:function(){return[]},getClass:function(){return Fs}}),Fs.getTriangleEdges=function(t,e){if(e[0]=t,e[1]=e[0].lNext(),e[2]=e[1].lNext(),e[2].lNext()!==e[0])throw new i("Edges do not form a triangle")},e(Gs.prototype,{visit:function(t){for(var e=t[0].orig().getCoordinate(),n=t[1].orig().getCoordinate(),i=t[2].orig().getCoordinate(),r=Si.circumcentre(e,n,i),s=new Ts(r),o=0;3>o;o++)t[o].rot().setOrig(s)},interfaces_:function(){return[As]},getClass:function(){return Gs}}),e(qs.prototype,{getTriangleEdges:function(){return this.triList},visit:function(t){this.triList.add(t.clone())},interfaces_:function(){return[As]},getClass:function(){return qs}}),e(Bs.prototype,{visit:function(t){this.triList.add([t[0].orig(),t[1].orig(),t[2].orig()])},getTriangleVertices:function(){return this.triList},interfaces_:function(){return[As]},getClass:function(){return Bs}}),e(zs.prototype,{checkTriangleSize:function(t){var e="";t.length>=2?e=se.toLineString(t[0],t[1]):t.length>=1&&(e=se.toPoint(t[0]))},visit:function(t){this.coordList.clear();for(var e=0;3>e;e++){var n=t[e].orig();this.coordList.add(n.getCoordinate())}if(this.coordList.size()>0){this.coordList.closeRing();var i=this.coordList.toCoordinateArray();if(4!==i.length)return null;this.triCoords.add(i)}},getTriangles:function(){return this.triCoords},interfaces_:function(){return[As]},getClass:function(){return zs}}),Fs.TriangleCircumcentreVisitor=Gs,Fs.TriangleEdgesListVisitor=qs,Fs.TriangleVertexListVisitor=Bs,Fs.TriangleCoordinatesVisitor=zs,Fs.EDGE_COINCIDENCE_TOL_FACTOR=1e3,e(Vs.prototype,{getLineSegment:function(){return this.ls},getEndZ:function(){var t=this.ls.getCoordinate(1);return t.z},getStartZ:function(){var t=this.ls.getCoordinate(0);return t.z},intersection:function(t){return this.ls.intersection(t.getLineSegment())},getStart:function(){return this.ls.getCoordinate(0)},getEnd:function(){return this.ls.getCoordinate(1)},getEndY:function(){var t=this.ls.getCoordinate(1);return t.y},getStartX:function(){var t=this.ls.getCoordinate(0);return t.x},equalsTopo:function(t){return this.ls.equalsTopo(t.getLineSegment())},getStartY:function(){var t=this.ls.getCoordinate(0);return t.y},setData:function(t){this.data=t},getData:function(){return this.data},getEndX:function(){var t=this.ls.getCoordinate(1);return t.x},toString:function(){return this.ls.toString()},interfaces_:function(){return[]},getClass:function(){return Vs}}),e(ks.prototype,{visit:function(t){},interfaces_:function(){return[]},getClass:function(){return ks}}),e(Ys.prototype,{isRepeated:function(){return this.count>1},getRight:function(){return this.right},getCoordinate:function(){return this.p},setLeft:function(t){this.left=t},getX:function(){return this.p.x},getData:function(){return this.data},getCount:function(){return this.count},getLeft:function(){return this.left},getY:function(){return this.p.y},increment:function(){this.count=this.count+1},setRight:function(t){this.right=t},interfaces_:function(){return[]},getClass:function(){return Ys}}),e(Us.prototype,{insert:function(){if(1===arguments.length){var t=arguments[0];return this.insert(t,null)}if(2===arguments.length){var e=arguments[0],n=arguments[1];if(null===this.root)return this.root=new Ys(e,n),this.root;if(this.tolerance>0){var i=this.findBestMatchNode(e);if(null!==i)return i.increment(),i}return this.insertExact(e,n)}},query:function(){var t=arguments,e=this;if(1===arguments.length){var n=arguments[0],i=new I;return this.query(n,i),i}if(2===arguments.length)if(arguments[0]instanceof C&&R(arguments[1],y))!function(){var n=t[0],i=t[1];e.queryNode(e.root,n,!0,{interfaces_:function(){return[ks]},visit:function(t){i.add(t)}})}();else if(arguments[0]instanceof C&&R(arguments[1],ks)){var r=arguments[0],s=arguments[1];this.queryNode(this.root,r,!0,s)}},queryNode:function(t,e,n,i){if(null===t)return null;var r=null,s=null,o=null;n?(r=e.getMinX(),s=e.getMaxX(),o=t.getX()):(r=e.getMinY(),s=e.getMaxY(),o=t.getY());var a=o>r,u=s>=o;a&&this.queryNode(t.getLeft(),e,!n,i),e.contains(t.getCoordinate())&&i.visit(t),u&&this.queryNode(t.getRight(),e,!n,i)},findBestMatchNode:function(t){var e=new Xs(t,this.tolerance);return this.query(e.queryEnvelope(),e),e.getNode()},isEmpty:function(){return null===this.root},insertExact:function(t,e){for(var n=this.root,i=this.root,r=!0,s=!0;null!==n;){if(null!==n){var o=t.distance(n.getCoordinate())<=this.tolerance;if(o)return n.increment(),n}s=r?t.x<n.getX():t.y<n.getY(),i=n,n=s?n.getLeft():n.getRight(),r=!r}this.numberOfNodes=this.numberOfNodes+1;var a=new Ys(t,e);return s?i.setLeft(a):i.setRight(a),a},interfaces_:function(){return[]},getClass:function(){return Us}}),Us.toCoordinates=function(){if(1===arguments.length){var t=arguments[0];return Us.toCoordinates(t,!1)}if(2===arguments.length){for(var e=arguments[0],n=arguments[1],i=new N,r=e.iterator();r.hasNext();)for(var s=r.next(),o=n?s.getCount():1,a=0;o>a;a++)i.add(s.getCoordinate(),!0);return i.toCoordinateArray()}},e(Xs.prototype,{visit:function(t){var e=this.p.distance(t.getCoordinate()),n=e<=this.tolerance;if(!n)return null;var i=!1;(null===this.matchNode||e<this.matchDist||null!==this.matchNode&&e===this.matchDist&&t.getCoordinate().compareTo(this.matchNode.getCoordinate())<1)&&(i=!0),i&&(this.matchNode=t,this.matchDist=e)},queryEnvelope:function(){var t=new C(this.p);return t.expandBy(this.tolerance),t},getNode:function(){return this.matchNode},interfaces_:function(){return[ks]},getClass:function(){return Xs}}),Us.BestMatchVisitor=Xs,e(Hs.prototype,{getInitialVertices:function(){return this.initialVertices},getKDT:function(){return this.kdt},enforceConstraints:function(){this.addConstraintVertices();var t=0,e=0;do e=this.enforceGabriel(this.segments),t++;while(e>0&&t<Hs.MAX_SPLIT_ITER)},insertSites:function(t){for(var e=t.iterator();e.hasNext();){var n=e.next();this.insertSite(n)}},getVertexFactory:function(){return this.vertexFactory},getPointArray:function(){for(var t=new Array(this.initialVertices.size()+this.segVertices.size()).fill(null),e=0,n=this.initialVertices.iterator();n.hasNext();){var i=n.next();t[e++]=i.getCoordinate()}for(var r=this.segVertices.iterator();r.hasNext();){var i=r.next();t[e++]=i.getCoordinate()}return t},setConstraints:function(t,e){this.segments=t,this.segVertices=e},computeConvexHull:function(){var t=new ie,e=this.getPointArray(),n=new me(e,t);this.convexHull=n.getConvexHull()},addConstraintVertices:function(){this.computeConvexHull(),this.insertSites(this.segVertices)},findNonGabrielPoint:function(t){var e=t.getStart(),n=t.getEnd(),i=new g((e.x+n.x)/2,(e.y+n.y)/2),s=e.distance(i),o=new C(i);o.expandBy(s);for(var a=this.kdt.query(o),u=null,l=r.MAX_VALUE,h=a.iterator();h.hasNext();){var c=h.next(),f=c.getCoordinate();if(!f.equals2D(e)&&!f.equals2D(n)){var d=i.distance(f);if(s>d){var p=d;(null===u||l>p)&&(u=f,l=p)}}}return u},getConstraintSegments:function(){return this.segments},setSplitPointFinder:function(t){this.splitFinder=t},getConvexHull:function(){return this.convexHull},getTolerance:function(){return this.tolerance},enforceGabriel:function(t){for(var e=new I,n=0,i=new I,r=t.iterator();r.hasNext();){var s=r.next(),o=this.findNonGabrielPoint(s);if(null!==o){this.splitPt=this.splitFinder.findSplitPoint(s,o);var a=this.createVertex(this.splitPt,s),u=(this.insertSite(a),new Vs(s.getStartX(),s.getStartY(),s.getStartZ(),a.getX(),a.getY(),a.getZ(),s.getData())),l=new Vs(a.getX(),a.getY(),a.getZ(),s.getEndX(),s.getEndY(),s.getEndZ(),s.getData());e.add(u),e.add(l),i.add(s),n+=1}}return t.removeAll(i),t.addAll(e),n},createVertex:function(){if(1===arguments.length){var t=arguments[0],e=null;return e=null!==this.vertexFactory?this.vertexFactory.createVertex(t,null):new Ps(t)}if(2===arguments.length){var n=arguments[0],i=arguments[1],e=null;return e=null!==this.vertexFactory?this.vertexFactory.createVertex(n,i):new Ps(n),e.setOnConstraint(!0),e}},getSubdivision:function(){return this.subdiv},computeBoundingBox:function(){var t=Hs.computeVertexEnvelope(this.initialVertices),e=Hs.computeVertexEnvelope(this.segVertices),n=new C(t);n.expandToInclude(e);var i=.2*n.getWidth(),r=.2*n.getHeight(),s=Math.max(i,r);this.computeAreaEnv=new C(n),this.computeAreaEnv.expandBy(s)},setVertexFactory:function(t){this.vertexFactory=t},formInitialDelaunay:function(){this.computeBoundingBox(),this.subdiv=new Fs(this.computeAreaEnv,this.tolerance),this.subdiv.setLocator(new Ms(this.subdiv)),this.incDel=new Os(this.subdiv),this.insertSites(this.initialVertices)},insertSite:function(){if(arguments[0]instanceof Ps){var t=arguments[0],e=this.kdt.insert(t.getCoordinate(),t);if(e.isRepeated()){var n=e.getData();return n.merge(t),n}return this.incDel.insertSite(t),t}if(arguments[0]instanceof g){var i=arguments[0];this.insertSite(this.createVertex(i))}},interfaces_:function(){return[]},getClass:function(){return Hs}}),Hs.computeVertexEnvelope=function(t){for(var e=new C,n=t.iterator();n.hasNext();){var i=n.next();e.expandToInclude(i.getCoordinate())}return e},Hs.MAX_SPLIT_ITER=99,e(Ws.prototype,{create:function(){if(null!==this.subdiv)return null;var t=Ws.envelope(this.siteCoords),e=Ws.toVertices(this.siteCoords);this.subdiv=new Fs(t,this.tolerance);var n=new Os(this.subdiv);n.insertSites(e)},setTolerance:function(t){this.tolerance=t},setSites:function(){if(arguments[0]instanceof B){var t=arguments[0];this.siteCoords=Ws.extractUniqueCoordinates(t)}else if(R(arguments[0],v)){var e=arguments[0];this.siteCoords=Ws.unique(H.toCoordinateArray(e))}},getEdges:function(t){return this.create(),this.subdiv.getEdges(t)},getSubdivision:function(){return this.create(),this.subdiv},getTriangles:function(t){return this.create(),this.subdiv.getTriangles(t)},interfaces_:function(){return[]},getClass:function(){return Ws}}),Ws.extractUniqueCoordinates=function(t){if(null===t)return new N;var e=t.getCoordinates();return Ws.unique(e)},Ws.envelope=function(t){for(var e=new C,n=t.iterator();n.hasNext();){var i=n.next();e.expandToInclude(i)}return e},Ws.unique=function(t){var e=H.copyDeep(t);ut.sort(e);var n=new N(e,!1);return n},Ws.toVertices=function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next();e.add(new Ts(i))}return e},e(js.prototype,{createSiteVertices:function(t){for(var e=new I,n=t.iterator();n.hasNext();){var i=n.next();this.constraintVertexMap.containsKey(i)||e.add(new Ps(i))}return e},create:function(){if(null!==this.subdiv)return null;var t=Ws.envelope(this.siteCoords),e=new I;null!==this.constraintLines&&(t.expandToInclude(this.constraintLines.getEnvelopeInternal()),this.createVertices(this.constraintLines),e=js.createConstraintSegments(this.constraintLines));var n=this.createSiteVertices(this.siteCoords),i=new Hs(n,this.tolerance);i.setConstraints(e,new I(this.constraintVertexMap.values())),i.formInitialDelaunay(),i.enforceConstraints(),this.subdiv=i.getSubdivision()},setTolerance:function(t){this.tolerance=t},setConstraints:function(t){this.constraintLines=t},setSites:function(t){this.siteCoords=Ws.extractUniqueCoordinates(t)},getEdges:function(t){return this.create(),this.subdiv.getEdges(t)},getSubdivision:function(){return this.create(),this.subdiv},getTriangles:function(t){return this.create(),this.subdiv.getTriangles(t)},createVertices:function(t){for(var e=t.getCoordinates(),n=0;n<e.length;n++){var i=new Ps(e[n]);this.constraintVertexMap.put(e[n],i)}},interfaces_:function(){return[]},getClass:function(){return js}}),js.createConstraintSegments=function(){if(1===arguments.length){for(var t=arguments[0],e=kn.getLines(t),n=new I,i=e.iterator();i.hasNext();){var r=i.next();js.createConstraintSegments(r,n)}return n}if(2===arguments.length)for(var s=arguments[0],o=arguments[1],a=s.getCoordinates(),i=1;i<a.length;i++)o.add(new Vs(a[i-1],a[i]))},e(Ks.prototype,{create:function(){if(null!==this.subdiv)return null;var t=Ws.envelope(this.siteCoords);this.diagramEnv=t;var e=Math.max(this.diagramEnv.getWidth(),this.diagramEnv.getHeight());this.diagramEnv.expandBy(e),null!==this.clipEnv&&this.diagramEnv.expandToInclude(this.clipEnv);var n=Ws.toVertices(this.siteCoords);this.subdiv=new Fs(t,this.tolerance);var i=new Os(this.subdiv);i.insertSites(n)},getDiagram:function(t){this.create();var e=this.subdiv.getVoronoiDiagram(t);return Ks.clipGeometryCollection(e,this.diagramEnv)},setTolerance:function(t){this.tolerance=t},setSites:function(){if(arguments[0]instanceof B){var t=arguments[0];this.siteCoords=Ws.extractUniqueCoordinates(t)}else if(R(arguments[0],v)){var e=arguments[0];this.siteCoords=Ws.unique(H.toCoordinateArray(e))}},setClipEnvelope:function(t){this.clipEnv=t},getSubdivision:function(){return this.create(),this.subdiv},interfaces_:function(){return[]},getClass:function(){return Ks}}),Ks.clipGeometryCollection=function(t,e){for(var n=t.getFactory().toGeometry(e),i=new I,r=0;r<t.getNumGeometries();r++){var s=t.getGeometryN(r),o=null;e.contains(s.getEnvelopeInternal())?o=s:e.intersects(s.getEnvelopeInternal())&&(o=n.intersection(s),o.setUserData(s.getUserData())),null===o||o.isEmpty()||i.add(o)}return t.getFactory().createGeometryCollection(ie.toGeometryArray(i))};var Ao=Object.freeze({ConformingDelaunayTriangulationBuilder:js,DelaunayTriangulationBuilder:Ws,VoronoiDiagramBuilder:Ks});e(Zs.prototype,{interfaces_:function(){return[]},getClass:function(){return Zs}}),Zs.union=function(t,e){if(t.isEmpty()||e.isEmpty()){if(t.isEmpty()&&e.isEmpty())return ii.createEmptyResult(ii.UNION,t,e,t.getFactory());if(t.isEmpty())return e.copy();if(e.isEmpty())return t.copy()}return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),si.overlayOp(t,e,ii.UNION)},e(B.prototype,{equalsTopo:function(t){return this.getEnvelopeInternal().equals(t.getEnvelopeInternal())?Jr.relate(this,t).isEquals(this.getDimension(),t.getDimension()):!1},union:function(){if(0===arguments.length)return is.union(this);if(1===arguments.length){var t=arguments[0];return Zs.union(this,t)}},isValid:function(){return ls.isValid(this)},intersection:function(t){if(this.isEmpty()||t.isEmpty())return ii.createEmptyResult(ii.INTERSECTION,this,t,this.factory);if(this.isGeometryCollection()){var e=t;return hn.map(this,{interfaces_:function(){return[MapOp]},map:function(t){return t.intersection(e)}})}return this.checkNotGeometryCollection(this),this.checkNotGeometryCollection(t),si.overlayOp(this,t,ii.INTERSECTION)},covers:function(t){return Jr.covers(this,t)},coveredBy:function(t){return Jr.coveredBy(this,t)},touches:function(t){return Jr.touches(this,t)},intersects:function(t){return Jr.intersects(this,t)},within:function(t){return Jr.within(this,t)},overlaps:function(t){return Jr.overlaps(this,t)},disjoint:function(t){return Jr.disjoint(this,t)},crosses:function(t){return Jr.crosses(this,t)},buffer:function(){if(1===arguments.length){var t=arguments[0];return dr.bufferOp(this,t)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return dr.bufferOp(this,e,n)}if(3===arguments.length){var i=arguments[0],r=arguments[1],s=arguments[2];return dr.bufferOp(this,i,r,s)}},convexHull:function(){return new me(this).getConvexHull()},relate:function(){for(var t=arguments.length,e=Array(t),n=0;t>n;n++)e[n]=arguments[n];return Jr.relate.apply(Jr,[this].concat(e))},getCentroid:function(){if(this.isEmpty())return this.factory.createPoint();var t=ge.getCentroid(this);return this.createPointFromInternalCoord(t,this)},getInteriorPoint:function(){if(this.isEmpty())return this.factory.createPoint();var t=null,e=this.getDimension();if(0===e){var n=new li(this);t=n.getInteriorPoint()}else if(1===e){var n=new ui(this);t=n.getInteriorPoint()}else{var n=new oi(this);t=n.getInteriorPoint()}return this.createPointFromInternalCoord(t,this)},symDifference:function(t){if(this.isEmpty()||t.isEmpty()){if(this.isEmpty()&&t.isEmpty())return ii.createEmptyResult(ii.SYMDIFFERENCE,this,t,this.factory);if(this.isEmpty())return t.copy();if(t.isEmpty())return this.copy()}return this.checkNotGeometryCollection(this),this.checkNotGeometryCollection(t),si.overlayOp(this,t,ii.SYMDIFFERENCE)},createPointFromInternalCoord:function(t,e){return e.getPrecisionModel().makePrecise(t),e.getFactory().createPoint(t)},toText:function(){var t=new se;return t.write(this)},toString:function(){this.toText()},contains:function(t){return Jr.contains(this,t)},difference:function(t){return this.isEmpty()?ii.createEmptyResult(ii.DIFFERENCE,this,t,this.factory):t.isEmpty()?this.copy():(this.checkNotGeometryCollection(this),this.checkNotGeometryCollection(t),si.overlayOp(this,t,ii.DIFFERENCE))},isSimple:function(){var t=new Wi(this);return t.isSimple()},isWithinDistance:function(t,e){var n=this.getEnvelopeInternal().distance(t.getEnvelopeInternal());return n>e?!1:xr.isWithinDistance(this,t,e)},distance:function(t){return xr.distance(this,t)},isEquivalentClass:function(t){return this.getClass()===t.getClass()}});var Fo="1.2.0 (d405c89)";t.version=Fo,t.algorithm=co,t.densify=fo,t.dissolve=go,t.geom=lo,t.geomgraph=po,t.index=yo,t.io=No,t.noding=Co,t.operation=_o,t.precision=Mo,t.simplify=Do,t.triangulate=Ao});

(function () {
'use strict';
sincronizar.$inject = ["$http", "$state", "urlBase", "MensagensService"];
angular
  .module('su-cadastro-imovel')
  .factory('SincronizarService', sincronizar);

function sincronizar ($http, $state, urlBase, MensagensService) {
  var sincronizaImovel = function (id) {
    return $http.get(urlBase + 'imovel/sincronizacao/' + id);
  };

  var sincronizacao = function (id) {
      var resultado = sincronizaImovel(id).then(function (resp) {
        var status = resp.status;
        if (status === 200) {
          imovelSincronizado(id)
            .then(function (resp) {
              var dsSpiunet = resp.data.dsSpiunet;
              if (!!dsSpiunet  && dsSpiunet !== "pend. sincronizacao") {
                MensagensService.exibirMensagemSucesso('Imóvel sincronizado com sucesso');
                $state.go('INCORPORACAO.detalharImovel', { id: id });
              } else {
                MensagensService.exibirMensagemErro('Imóvel não sincronizado.');
              }
            })
        }else {
          MensagensService.exibirMensagemErro('Erro na sincronização.');
      }
      return resultado;
      });
      return resultado;
  };

  var imovelSincronizado = function (id) {
      return $http.get(urlBase + 'imovel/imovelSincronizado/' + id);
    };

  return {
    sincronizacao:sincronizacao,
    imovelSincronizado:imovelSincronizado,
    sincronizaImovel: sincronizaImovel
  };
}
})();

(function () {
    "use strict";
    angular.module("su-cadastro-imovel").factory("PaginacaoFactory", paginacaoFactory);

    function paginacaoFactory() {

        function PaginacaoDefault() {
            this.limitOptions = [5, 10, 50, 100];
            this.limit = 10;
            this.page = 1;
            this.totalElements = 0;
        }

        return {
            getDefault: function () {
                return new PaginacaoDefault();
            }
        };
    }
})();

(function () {
    "use strict";
    mensagensService.$inject = ["$mdToast"];
    angular.module("su-cadastro-imovel").factory("MensagensService", mensagensService);

    function mensagensService($mdToast) {

        var TEMAS = {
            ERRO: "error-toast",
            SUCESSO: "success-toast"
        };

        var MENSAGENS = {
            CAMPOS_OBRIGATORIOS: "Necessário preencher os campos obrigatórios"
        };

        function exibirMensagem(msg, tema) {
            $mdToast.show(
                $mdToast.simple()
                    .content(msg)
                    .theme(tema)
                    .position('top right')
                    .hideDelay(15000)
            );
        }

        function mensagemErro(msg) {
            exibirMensagem(msg, TEMAS.ERRO);
        }

        function mensagemSucesso(msg) {
            exibirMensagem(msg, TEMAS.SUCESSO);
        }

        function mensagemCamposObrigatorios() {
            exibirMensagem(MENSAGENS.CAMPOS_OBRIGATORIOS, TEMAS.ERRO);
        }

        return {
            exibirMensagemErro: mensagemErro,
            exibirMensagemSucesso: mensagemSucesso,
            exibirMensagemCamposObrigatorios: mensagemCamposObrigatorios
        };
    }
})();

(function () {
  'use strict';
  service.$inject = ["$http", "urlBase"];
  angular.module('su-cadastro-imovel').factory('IntegracaoSeiService', service);

  function service($http, urlBase) {
    var URL_COMUM = urlBase + 'barramentoSei';

    var recuperarDocumentoSei = function (num, uf) {
      return $http.get(
        URL_COMUM + '/recuperar-documento-sei/' + num + '/' + uf
      );
    };

    var recuperarProcedimentoSei = function (num, uf) {
      return $http.get(
        URL_COMUM + '/recuperar-procedimento-sei/' + num + '/' + uf
      );
    };

    var criarProcessoSei = function (uf) {
      return $http.get(URL_COMUM + '/criar-processo-sei/' + uf);
    };
    return {
      recuperarDocumentoSei: recuperarDocumentoSei,
      recuperarProcedimentoSei: recuperarProcedimentoSei,
      criarProcessoSei: criarProcessoSei
    };
  }
})();

(function () {
  'use strict';
  cpfCnpjService.$inject = ["MensagensService"];
  angular
    .module('su-cadastro-imovel')
    .factory('CpfCnpjService', cpfCnpjService);

  function cpfCnpjService(MensagensService) {
    function validaCpf(cpf) {      
      if (
        !cpf ||
        cpf.length != 11 ||
        cpf == '00000000000' ||
        cpf == '11111111111' ||
        cpf == '22222222222' ||
        cpf == '33333333333' ||
        cpf == '44444444444' ||
        cpf == '55555555555' ||
        cpf == '66666666666' ||
        cpf == '77777777777' ||
        cpf == '88888888888' ||
        cpf == '99999999999'
      )
        return false;
      var soma = 0;
      var resto;
      for (var i = 1; i <= 9; i++)
        soma = soma + parseInt(cpf.substring(i - 1, i)) * (11 - i);
      resto = (soma * 10) % 11;
      if (resto == 10 || resto == 11) resto = 0;
      if (resto != parseInt(cpf.substring(9, 10))) return false;
      soma = 0;
      for (var i = 1; i <= 10; i++)
        soma = soma + parseInt(cpf.substring(i - 1, i)) * (12 - i);
      resto = (soma * 10) % 11;
      if (resto == 10 || resto == 11) resto = 0;
      if (resto != parseInt(cpf.substring(10, 11))) return false;
      return true;
    }

    function removeMascaraCNPJ(cnpj) {
        const regexCaracteresMascara = /[./-]/g;
        return cnpj.replace(regexCaracteresMascara, "");
    }

    function validaCnpj(cnpj) {    
        
        const tamanhoCNPJSemDV = 12;
        const regexCNPJSemDV = /^([A-Z\d]){12}$/;
        const regexCNPJ = /^([A-Z\d]){12}(\d){2}$/;                
        const regexCaracteresNaoPermitidos = /[^A-Z\d./-]/i;
        const valorBase = "0".charCodeAt(0);
        const pesosDV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const cnpjZerado = "00000000000000";

        if (!regexCaracteresNaoPermitidos.test(cnpj)) {                
            var cnpjSemMascara = removeMascaraCNPJ(cnpj);
            if (regexCNPJ.test(cnpjSemMascara) && cnpjSemMascara.substring(0, tamanhoCNPJSemDV) !== cnpjZerado.substring(0, tamanhoCNPJSemDV)) {
                var somatorioDV1 = 0;
                var somatorioDV2 = 0;
                for (var i = 0; i < tamanhoCNPJSemDV; i++) {
                    const asciiDigito = cnpjSemMascara.charCodeAt(i) - valorBase;
                    somatorioDV1 += asciiDigito * pesosDV[i + 1];
                    somatorioDV2 += asciiDigito * pesosDV[i];
                }
                const dv1 = somatorioDV1 % 11 < 2 ? 0 : 11 - (somatorioDV1 % 11);
                somatorioDV2 += dv1 * pesosDV[tamanhoCNPJSemDV];
                const dv2 = somatorioDV2 % 11 < 2 ? 0 : 11 - (somatorioDV2 % 11);
                const cnpjCalculado = cnpjSemMascara.substring(0, tamanhoCNPJSemDV) + dv1 + dv2;
                return (cnpjCalculado === cnpjSemMascara);
            }
        }
        return false;
    }

    function format(cleanValue) {
      var formatedValue;
      if (cleanValue.length > 11) {
        formatedValue = cleanValue.replace(
          /([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})([A-Z0-9]{2})/g,
          '$1.$2.$3/$4-$5'
        );
        return formatedValue.trim().replace(/[^A-Z0-9]$/, '');

      } else {
        formatedValue = cleanValue.replace(
          /(\d{3})(\d{3})(\d{3})(\d{2})/g,
          '$1.$2.$3-$4'
        );
        return formatedValue.trim().replace(/[^0-9]$/, '');
      }

      
    }

    function formataCpfCnpj(documento) {
      var dadoTratado = documento.replace(/[^A-Z0-9]/g, '').slice(0, 14);
      var isCnpj = false;
      if (dadoTratado.length > 11) isCnpj = true;

      if (isCnpj) {
        if (!validaCnpj(dadoTratado)) {
          MensagensService.exibirMensagemErro('CNPJ inválido');
          return null;
        }
      } else {
        if (!validaCpf(dadoTratado)) {
          MensagensService.exibirMensagemErro('CPF inválido');
          return null;
        }
      }
      return format(dadoTratado);
    }
    return {
      formataCpfCnpj: formataCpfCnpj,
      validaCnpj: validaCnpj,
      validaCpf: validaCpf,
    };
  }
})();

(function () {
'use strict';
service.$inject = ["$http", "$resource", "urlBase"];
angular
  .module('su-cadastro-imovel')
  .factory('comumService', service);

function service ($http, $resource, urlBase) {

  var buscarUrlsMenusPorAmbiente = function () {
    return $http.get(urlBase + 'comum/url-menu');
  };

  var obterPermissoes = function (cpf, modulo) {
    return $http.get(urlBase + 'comum/buscar-permissoes/' + cpf + '/' + modulo);
  };

  var buscarUrlGeoServer = function(){
    return $http.get(urlBase + 'comum/buscar-url-geoserver');
  };

  var buscarUrlGeoWebCache = function(){
    return $http.get(urlBase + 'comum/buscar-url-geowebcache');
  };

  var ehAmbienteDesenvolvimento = function () {
    var location = window.location.host;
    if(location.includes("des-") || location.includes("localhost"))
      return true;
    else
      return false;
  };


  return {
    buscarUrlGeoWebCache:buscarUrlGeoWebCache,
    buscarUrlGeoServer:buscarUrlGeoServer,
    buscarUrlsMenusPorAmbiente: buscarUrlsMenusPorAmbiente,
    obterPermissoes: obterPermissoes,
    ehAmbienteDesenvolvimento: ehAmbienteDesenvolvimento
  };
}


})();

( function () {
    "use strict";
    service.$inject = ["$window", "$http", "urlBase"];
    angular.module("su-cadastro-imovel")
    .service("ArquivoService", service);

    function service($window, $http, urlBase) {

        function uploadArquivo(arquivo) {

            var fd = new FormData();
            fd.append('arquivo', arquivo);

            return $http.post(urlBase + "arquivo/upload", fd, {
                transformRequest: angular.identity,
                headers: {
                    'Content-Type': undefined
                }
            });
        }

        function uploadShapefile(arquivo, codMunicipioImovel) {

            var fd = new FormData();
            fd.append('arquivo', arquivo);
            fd.append('codMunicipioImovel', codMunicipioImovel);

            return $http.post(urlBase + "arquivo/shapefile/upload", fd, {
                transformRequest: angular.identity,
                headers: {
                    'Content-Type': undefined
                }
            });
        }

        function getUrlFromBase64(string) {
            var url = $window.URL || $window.webkitURL;
            var blob = new Blob([getBytes(string)], {type: 'application/octet-stream'});
            return url.createObjectURL(blob);
        }

        function getBytes(base64) {
            var binaryString = $window.atob(base64);
            var binaryLen = binaryString.length;
            var bytes = new Uint8Array(binaryLen);
            for (var i = 0; i < binaryLen; i++) {
                var ascii = binaryString.charCodeAt(i);
                bytes[i] = ascii;
            }
            return bytes;
        }

        return {
            uploadArquivo: uploadArquivo,
            uploadShapefile: uploadShapefile,
            getUrlFromBase64: getUrlFromBase64
        };
    }
})();

var URL_AVATAR_ICONS = 'assets/img/icons/avatars.svg';
var URL_ICON_MENU = 'assets/img/icons/menu.svg';
var URL_ICON_SHARE = 'assets/img/icons/share.svg';
var URL_ICON_PERSON = 'assets/img/icons/ic_person_48px.svg';
var URL_LOGOUT = 'assets/img/icons/ic_power_settings_new_48px.svg';
(function () {
angular.module('su-cadastro-imovel')
    .run(["$rootScope", "$location", "$window", "$http", "$state", "$transitions", function ($rootScope, $location, $window, $http, $state, $transitions) {

        /*function estaAutenticado() {
            var deferred = $q.defer();

            var watchUsuario = $rootScope.$watch("usuarioLogado", function () {
                if ($rootScope.usuarioLogado) {
                    deferred.resolve($rootScope.usuarioLogado);
                    watchUsuario();
                }
            });

            return deferred.promise;
        }

        $rootScope.$on('$viewContentLoading', function() {
            estaAutenticado().then(function (result) {
            });
        });*/

        $transitions.onEnter({}, function (transition) {
            $state.previousState = {
                name: transition.from().name,
                params: transition.params("from")
            };
        });

        $state.defaultErrorHandler(function (erro) {
            $state.go(erro.redirectTo);
        });

        $rootScope.$on('$stateChangeStart', function (event, toState, toStateParams) {
            $rootScope.toState = toState;
            $rootScope.toStateParams = toStateParams;

            // Colocar aqui o código de inicialização depois de integrado com sistema de segurança

        });

        $rootScope.$on('$stateChangeSuccess',  function(event, toState, toParams, fromState, fromParams) {
            var titleKey = 'Sistema Unificado de Patrimônio' ;
            if (toState.name != 'login') {
                $rootScope.previousStateName = fromState.name;
                $rootScope.previousStateParams = fromParams;
            }
            if (toState.data && toState.data.pageTitle) {
                titleKey = toState.data.pageTitle;
            }
            $window.document.title = titleKey;
        });

        $rootScope.back = function() {
            if ($rootScope.previousStateName === 'activate' || $state.get($rootScope.previousStateName) === null) {
                $state.go('INCORPORACAO.home');
            } else {
                $state.go($rootScope.previousStateName, $rootScope.previousStateParams);
            }
        };

    }]);
})();

(function () {
    'use strict';
    angular.module('su-cadastro-imovel').config(["$stateProvider", "$urlRouterProvider", function ($stateProvider, $urlRouterProvider) {

        $urlRouterProvider.when("/incorporacao/pesquisarImovel", "/incorporacao/pesquisarImovel/simples");

        var verificarPermissaoParaPagina = function (permissao) {
            return ["$state", "$q", "$timeout", "$http", "$rootScope", "$interval", function ($state, $q, $timeout, $http, $rootScope, $interval) {
                var defer = $q.defer();
                if ($rootScope.usuarioLogado && $rootScope.usuarioLogado.id) {
                    if (!permissao) {
                        defer.resolve();
                    } else {
                        if ($rootScope.usuarioLogado.permissoes && $rootScope.usuarioLogado.permissoes.indexOf(permissao) != -1) {
                            defer.resolve();
                        } else {
                            defer.reject("usuario-sem-permissao");
                        }
                    }
                } else {

                    if (!permissao) {
                        defer.resolve();
                    } else {
                        var intervalPromise2 = $interval(function () {

                            if ($rootScope.usuarioLogado) {

                                if ($rootScope.usuarioLogado && $rootScope.usuarioLogado.id) {
                                    if (!permissao) {
                                        defer.resolve();
                                    } else {
                                        if ($rootScope.usuarioLogado.permissoes && $rootScope.usuarioLogado.permissoes.indexOf(permissao) != -1) {
                                            defer.resolve();
                                        } else {
                                            defer.reject("usuario-sem-permissao");
                                        }
                                    }
                                } else {
                                    defer.reject("usuario-nao-logado");
                                }

                                $interval.cancel(intervalPromise2);
                            }
                        }, 1000);

                    }
                }

                return defer.promise;
            }];
        };

        var states = [
            {
                state: 'INCORPORACAO',
                abstract: true,
                url: "/incorporacao",
                template: '<div id="su-incorporacao" ui-view></div>',
                permissao: 'CAD_IMOVEL.CADASTRAR_IMOVEL'
            },
            {
                state: 'INCORPORACAO.inserirCadastro',
                url: "/inserirCadastro",
                templateUrl: "scripts-cadastro-imovel/pages/imovel/view/imovel-terreno.tmpl.html",
                controller: 'ImovelController as imovelCtrl',
                resolve: {
                    camposAjuda: ['camposAjudaService', 'HINTS_CONTANTS', function (camposAjudaService, HINTS_CONTANTS) {

                        var codigosSistemicos = [HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_LOCALIZACAO_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_DADOS_GERAIS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_TERRENO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_PROPRIETARIO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_TITULO_AQUIS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UPLOAD_ARQUIVOS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UPLOAD_FOTOS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UPLOAD_ARQ_TERRENO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_TERCEIROS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_DADOS_MEDIDOR,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_DOCUMENTACAO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_BENFEITORIA,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UN_AUTONOMA,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_SUSPENSAO];
                        return camposAjudaService.buscarPorFuncionalidade(codigosSistemicos)
                            .then(function (res) {
                                var hints = [];
                                if (res) {
                                    hints = res.data.resposta;
                                }
                                return hints;
                            });
                    }],
                    action: ['Enums', function (Enums) {
                        return Enums.Action['EDIT'];
                    }]
                },
                permissao: 'CAD_IMOVEL.CADASTRAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Inserir Cadastro'
                }
            }, {
                state: 'INCORPORACAO.alterarCadastro',
                url: "/alterarCadastro/:id/:tab",
                templateUrl: "scripts-cadastro-imovel/pages/imovel/view/editar-imovel/editar-imovel.tmp.html",
                controller: 'EditarImovelController as vm',
                resolve: {
                    imovelResolve: ['$stateParams', 'DetalharImovel', 'tabsIncorporacao', '$filter', function ($stateParams, DetalharImovel, tabsIncorporacao, $filter) {
                        return DetalharImovel.get({ id: $stateParams.id }).$promise.then(function (data) {
                            var imovel = data.imovelAlterado;

                            if (data.imovelAlterado.unidadeAutonoma && data.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral) {
                                data.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral = $filter('number')(data.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral, 10);
                            }

                            if (data.imovelAlterado.unidadeAutonoma && data.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial) {
                                data.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial = $filter('number')(data.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral, 10);
                            }

                            if (imovel.situacaoImovel.id === 2 && imovel.situacaoCadastro.id === 3) {
                                tabsIncorporacao.addTabResultadoHomologacao();
                            } else {
                                tabsIncorporacao.removeTabResultadoHomologacao();
                            }

                            return data;
                        });
                    }],
                    pendenciaResolve: ['$stateParams', 'PendenciasService', function ($stateParams, PendenciasService) {
                        return PendenciasService.getPendenciaPorImovel($stateParams.id).query().$promise.then(function (pendencias) {
                            return pendencias;
                        });
                    }],
                    camposAjuda: ['camposAjudaService', 'HINTS_CONTANTS', function (camposAjudaService, HINTS_CONTANTS) {

                        var codigosSistemicos = [HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_LOCALIZACAO_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_DADOS_GERAIS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_TERRENO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_PROPRIETARIO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_TITULO_AQUIS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UPLOAD_ARQUIVOS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UPLOAD_FOTOS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UPLOAD_ARQ_TERRENO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_TERCEIROS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_DADOS_MEDIDOR,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_DOCUMENTACAO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_BENFEITORIA,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UN_AUTONOMA,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_SUSPENSAO];
                        return camposAjudaService.buscarPorFuncionalidade(codigosSistemicos)
                            .then(function (res) {
                                var hints = [];
                                if (res) {
                                    hints = res.data.resposta;
                                }
                                return hints;
                            });
                    }],
                    action: ['Enums', function (Enums) {
                        return Enums.Action['EDIT'];
                    }]
                },
                permissao: 'CAD_IMOVEL.CADASTRAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Alterar Cadastro'
                }
            }, {
                state: 'INCORPORACAO.imovelConsultar',
                url: "/consultarImovel",
                templateUrl: "scripts-cadastro-imovel/pages/imovel/view/consultar-imovel.tmpl.html",
                controller: 'ConsultarImovelController as vm',
                permissao: 'CAD_IMOVEL.CONSULTAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Consultar Imóvel'
                },
                resolve: {
                    camposAjuda: ['camposAjudaService', 'HINTS_CONTANTS', function (camposAjudaService, HINTS_CONTANTS) {

                        var codigosSistemicos = [HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CONSULTA_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_SUSPENSAO];
                        return camposAjudaService.buscarPorFuncionalidade(codigosSistemicos)
                            .then(function (res) {
                                var hints = [];
                                if (res) {
                                    hints = res.data.resposta;
                                }
                                return hints;
                            });
                    }]
                }

            }, {
                state: 'INCORPORACAO.pesquisarImovel',
                url: "/pesquisarImovel",
                abstract: true,
                templateUrl: "scripts-cadastro-imovel/pages/imovel/view/pesquisar-imovel.tmpl.html",
                controller: 'PesquisarImovelController as piCtrl',
                permissao: 'CAD_IMOVEL.PESQUISAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Pesquisar Imóvel'
                },
                resolve: {
                    camposAjuda: ['camposAjudaService', 'HINTS_CONTANTS', function (camposAjudaService, HINTS_CONTANTS) {

                        var codigosSistemicos = [HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_PESQ_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_SUSPENSAO];
                        return camposAjudaService.buscarPorFuncionalidade(codigosSistemicos)
                            .then(function (res) {
                                var hints = [];
                                if (res) {
                                    hints = res.data.resposta;
                                }
                                return hints;
                            });
                    }]
                }

            }, {
                state: 'INCORPORACAO.pesquisarImovel.pesquisaSimples',
                url: "/simples",
                templateUrl: "scripts-cadastro-imovel/pages/imovel/view/pesquisar-imovel/filtro-simples.tmpl.html",
                permissao: 'CAD_IMOVEL.PESQUISAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Pesquisar Imóvel Simples'
                }
            }, {
                state: 'INCORPORACAO.pesquisarImovel.pesquisaAvancada',
                url: "/avancada",
                templateUrl: "scripts-cadastro-imovel/pages/imovel/view/pesquisar-imovel/filtroavancado.tmpl.html",
                permissao: 'CAD_IMOVEL.PESQUISAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Pesquisar Imóvel Avançada'
                }
            }, {
                state: 'INCORPORACAO.pendenciaImovel',
                url: "/pendenciaImovel",
                templateUrl: "scripts-cadastro-imovel/pages/pendencia/view/pendencia-imovel.tmpl.html",
                controller: 'ConsultarPendenciaController as consultarPendenciaCtrl',
                permissao: 'CAD_IMOVEL.CONSULTAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Consultar Imóveis com Pendência'
                },
                resolve: {
                    camposAjuda: ['camposAjudaService', 'HINTS_CONTANTS', function (camposAjudaService, HINTS_CONTANTS) {

                        var codigosSistemicos = [HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CONSULTA_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_SUSPENSAO];
                        return camposAjudaService.buscarPorFuncionalidade(codigosSistemicos)
                            .then(function (res) {
                                var hints = [];
                                if (res) {
                                    hints = res.data.resposta;
                                }
                                return hints;
                            });
                    }]
                }

            }, {
                state: 'INCORPORACAO.homologarImovel',
                url: "/homologarImovel",
                templateUrl: "scripts-cadastro-imovel/pages/homologar/view/homologar-imovel.tmpl.html",
                controller: 'ConsultarHomologarController as consultarHomologarCtrl',
                permissao: 'CAD_IMOVEL.HOMOLOGAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Consultar Imóvel para Homologação'
                },
                resolve: {
                    camposAjuda: ['camposAjudaService', 'HINTS_CONTANTS', function (camposAjudaService, HINTS_CONTANTS) {

                        var codigosSistemicos = [HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_HOM_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_SUSPENSAO];
                        return camposAjudaService.buscarPorFuncionalidade(codigosSistemicos)
                            .then(function (res) {
                                var hints = [];
                                if (res) {
                                    hints = res.data.resposta;
                                }
                                return hints;
                            });
                    }]
                }

            }, {
                state: 'INCORPORACAO.homologarImovelDetalhar',
                url: "/homologarImovel/:id",
                templateUrl: "scripts-cadastro-imovel/pages/homologar/view/homologar-imovel-detalhar-novo.tmpl.html",
                controller: 'HomologarImovelController as homCtrl',
                resolve: {
                    imovelResolve: ['$stateParams', 'HomologacaoImovelService', '$q', function ($stateParams, HomologacaoImovelService, $q) {

                        var deferred = $q.defer();

                        HomologacaoImovelService.buscarNaoHomologado($stateParams.id).then(
                            function (result) {
                                deferred.resolve(result.data);
                            },
                            function () {
                                deferred.reject({ redirectTo: "INCORPORACAO.homologarImovel" });
                            }
                        );

                        return deferred.promise;
                    }]
                },
                permissao: 'CAD_IMOVEL.HOMOLOGAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Homologar Imóvel'
                }
            }, {
                state: 'INCORPORACAO.detalhesHomologacao',
                url: "/homologarImovel/:id/detalhar",
                templateUrl: "scripts-cadastro-imovel/pages/homologar/view/homologar-imovel-detalhar-novo.tmpl.html",
                controller: 'HomologarImovelController as homCtrl',
                resolve: {
                    imovelResolve: ['$stateParams', 'HomologacaoImovelService', '$q', function ($stateParams, HomologacaoImovelService, $q) {

                        var deferred = $q.defer();

                        HomologacaoImovelService.buscarRecusado($stateParams.id).then(
                            function (result) {
                                deferred.resolve(result.data);
                            },
                            function () {
                                deferred.reject({ redirectTo: "INCORPORACAO.homologarImovel" });
                            }
                        );

                        return deferred.promise;
                    }]
                },
                permissao: 'CAD_IMOVEL.HOMOLOGAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Detalhar Homologação do Imóvel'
                }
            }, {
                state: 'INCORPORACAO.detalharImovel',
                url: "/detalharImovel/:id",
                templateUrl: "scripts-cadastro-imovel/pages/imovel/view/abas.tmpl.html",
                controller: 'EditarImovelController as vm',
                resolve: {
                    imovelResolve: ['$stateParams', 'DetalharImovel', 'tabsIncorporacao', function ($stateParams, DetalharImovel, tabsIncorporacao) {
                        return DetalharImovel.get({ id: $stateParams.id }).$promise.then(function (data) {
                            var imovel = data.imovelAlterado;

                            if (imovel.situacaoImovel.id === 2 && imovel.situacaoCadastro.id === 3) {
                                tabsIncorporacao.addTabResultadoHomologacao();
                            } else {
                                tabsIncorporacao.removeTabResultadoHomologacao();
                            }

                            return data;
                        });
                    }],
                    pendenciaResolve: ['$stateParams', 'PendenciasService', function ($stateParams, PendenciasService) {
                        return PendenciasService.getPendenciaPorImovel($stateParams.id).query().$promise.then(function (pendencias) {
                            return pendencias;
                        });
                    }],
                    camposAjuda: ['camposAjudaService', 'HINTS_CONTANTS', function (camposAjudaService, HINTS_CONTANTS) {

                        var codigosSistemicos = [HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_LOCALIZACAO_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_DADOS_GERAIS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_TERRENO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_PROPRIETARIO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_TITULO_AQUIS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UPLOAD_ARQUIVOS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UPLOAD_FOTOS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UPLOAD_ARQ_TERRENO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_TERCEIROS,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_DADOS_MEDIDOR,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_DOCUMENTACAO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_BENFEITORIA,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_UN_AUTONOMA,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_SUSPENSAO];
                        return camposAjudaService.buscarPorFuncionalidade(codigosSistemicos)
                            .then(function (res) {
                                var hints = [];
                                if (res) {
                                    hints = res.data.resposta;
                                }
                                return hints;
                            });
                    }],
                    action: ['Enums', function (Enums) {
                        return Enums.Action['DETAIL'];
                    }]
                },
                permissao: 'CAD_IMOVEL.DETALHAR_IMOVEL',
                ncyBreadcrumb: {
                    label: 'Detalhar Imóvel'
                }
            }, {
                state: 'INCORPORACAO.suspenderImovel',
                url: "/suspenderImovel/cadastro",
                templateUrl: "scripts-cadastro-imovel/pages/suspensao/view/cadastroSuspenderImovel.tmpl.html",
                controller: 'CadastroSuspenderImovelController as cadSuspensaoCtrl',
                permissao: 'SUSPENSAO.CADASTRAR',
                ncyBreadcrumb: {
                    label: 'Suspender Imóvel '
                },
                resolve: {
                    camposAjuda: ['camposAjudaService', 'HINTS_CONTANTS', function (camposAjudaService, HINTS_CONTANTS) {

                        var codigosSistemicos = [HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CADAST_SUSPENSAO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_SUSPENSAO];
                        return camposAjudaService.buscarPorFuncionalidade(codigosSistemicos)
                            .then(function (res) {
                                var hints = [];
                                if (res) {
                                    hints = res.data.resposta;
                                }
                                return hints;
                            });
                    }]
                }
            }, {
                state: 'INCORPORACAO.consultarSuspensao',
                url: "/consultarSuspensao",
                templateUrl: "scripts-cadastro-imovel/pages/suspensao/view/consultar-suspensao-imovel.tmpl.html",
                controller: 'ConsultarSuspencaoImovelController as consultaSuspensaoCtrl',
                permissao: 'SUSPENSAO.CONSULTAR',
                ncyBreadcrumb: {
                    label: 'Consultar Suspensão'
                },
                resolve: {
                    camposAjuda: ['camposAjudaService', 'HINTS_CONTANTS', function (camposAjudaService, HINTS_CONTANTS) {

                        var codigosSistemicos = [HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CONSULTA_SUSPENSAO,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_IMOVEL,
                        HINTS_CONTANTS.CODIGOS_SISTEMICO_FORM.INC_FORM_CANCEL_SUSPENSAO];
                        return camposAjudaService.buscarPorFuncionalidade(codigosSistemicos)
                            .then(function (res) {
                                var hints = [];
                                if (res) {
                                    hints = res.data.resposta;
                                }
                                return hints;
                            });
                    }]
                }

            }, {
                state: 'INCORPORACAO.detalharSuspensao',
                url: "/suspenderImovel/detalhar/:id",
                templateUrl: "scripts-cadastro-imovel/pages/suspensao/view/detalhes-suspensao-imovel.tmpl.html",
                controller: 'DetalharSuspensaoController as detalharSuspensaoCtrl',
                resolve: {
                    suspensaoDetalharResolve: ['$stateParams', 'ImovelSuspensaoService', function ($stateParams, ImovelSuspensaoService) {
                        return ImovelSuspensaoService.detalharSuspensao({ id: $stateParams.id }).then(function (data) {
                            return data;
                        });
                    }]
                },
                permissao: 'SUSPENSAO.DETALHAR',
                ncyBreadcrumb: {
                    label: 'Detalhar Suspensão'
                }
            }];

        angular.forEach(states, function (state) {

            var resolve = {};

            for (var index in state.resolve) {
                resolve[index] = state.resolve[index];
            }

            // resolve.verificarPermissaoParaPagina = verificarPermissaoParaPagina(state.permissao);

            $stateProvider
                .state(state.state, {
                    abstract: state.abstract,
                    template: state.template,
                    url: state.url,
                    templateUrl: state.templateUrl,
                    controller: state.controller,
                    resolve: resolve,
                    params: state.params,
                    ncyBreadcrumb: state.ncyBreadcrumb
                });
        });

    }]);

})();

(function () {
'use strict';
angular.module('su-cadastro-imovel')
    .controller('MenuController', ["$mdSidenav", function($mdSidenav) {
            var self = this;

      var init = function () {
        /**
         * Essa classe é adicionada automaticamente pela diretiva md-position-mode
         * essa diretiva é necessária em cada md-menu para nao printar erro do angular material no console
         * a remoção dessa classe é necessária para não alterar o tamanho do menu
         */
     angular.element(".md-menu-bar-menu").removeClass("md-dense");
      };

      init();

      self.toggleMenu= function() {
        $mdSidenav('left').toggle();
      };
      self.closeMenu= function() {
        $mdSidenav('left').close();
      }

            var sections = [];
            sections.push({
                    name: 'Imóvel',
                    type: 'heading',
                    children: [
                            {
                                    name: 'Dados Gerais',
                                    type: 'toggle',
                                    pages: [{
                                        name: 'Manter Dados do Imóvel',
                                        state: 'INCORPORACAO.inserirCadastro',
                                        type: 'link'
                                    },{
                                        name : 'Homologar Imóvel',
                                        state: 'INCORPORACAO.homologarImovel',
                                        type: 'link'
                                    },{
                                        name : 'Vetorizar terreno',
                                        state: 'INCORPORACAO.vetorizarTerreno',
                                        type: 'link'
                                    },{
                                        name:'Consultar Imóvel',
                                        state:'INCORPORACAO.imovelConsultar',
                                        type:'link'
                                    },{
                                        name:'Consultar Suspenção Imóvel',
                                        state:'INCORPORACAO.consultarSuspensao',
                                        type:'link'
                                    }]
                            }, {
                                    name: 'Dados Contábeis',
                                    type: 'toggle',
                                    pages: [
                                            {
                                                    name: 'Manter Laudo de Avaliação',
                                                    state: 'INCORPORACAO.manterLaudo',
                                                    type: 'link'
                                            },
                                            {
                                                    name: 'Atualizar valor do imóvel',
                                                    state: 'INCORPORACAO.atualizarValor',
                                                    type: 'link'
                                            },
                                            {
                                                    name: 'Depreciar imóvel',
                                                    state: 'INCORPORACAO.depreciarImovel',
                                                    type: 'link'
                                            },
                                            {
                                                    name: 'Manter valor da PVG',
                                                    state: 'INCORPORACAO.manterValorPVG',
                                                    type: 'link'
                                            },
                                            {
                                                    name: 'Enviar dados contábeis ao SIAFI',
                                                state: 'INCORPORACAO.enviarDadosSiafi',
                                                    type: 'link'
                                            }
                                    ]
                            }
                    ]
            });
            sections.push({
                    name: 'License',
                    url:  'license',
                    type: 'link',

                    // Add a hidden section so that the title in the toolbar is properly set
                    hidden: true
            });

            var menu = {
                    sections: sections,

                    selectSection: function(section) {
                            self.openedSection = section;
                    },
                    toggleSelectSection: function(section) {
                            self.openedSection = (self.openedSection === section ? null : section);
                    },
                    isSectionSelected: function(section) {
                            return self.openedSection === section;
                    },

                    selectPage: function(section, page) {
                            self.currentSection = section;
                            self.currentPage = page;
                    },
                    isPageSelected: function(page) {
                            return self.currentPage === page;
                    }
            };
            self.menu = menu;

            // Methods used by menuLink and menuToggle directives
            self.isOpen = isOpen;
            self.isSelected = isSelected;
            self.toggleOpen = toggleOpen;
            self.autoFocusContent = false;

            function isSelected(page) {
                    return menu.isPageSelected(page);
            }

            function isSectionSelected(section) {
                    var selected = false;
                    var openedSection = menu.openedSection;
                    if(openedSection === section){
                            selected = true;
                    }
                    else if(section.children) {
                            section.children.forEach(function(childSection) {
                                    if(childSection === openedSection){
                                            selected = true;
                                    }
                            });
                    }
                    return selected;
            }

            function isOpen(section) {
                    return menu.isSectionSelected(section);
            }

            function toggleOpen(section) {
                    menu.toggleSelectSection(section);
            }

    }]);
})();

(function () {
  'use strict';
angular.module('su-cadastro-imovel').constant('NOTIFICADORES',
	{

		IMOVEL:'IMOVEL',

		// TERRENO

		TERRENOEDICAO: 'TERRENOEDICAO',

		// GEOMETRIA

		LIMPARDESENHOCADASTRAL: 'LIMPARDESENHOCADASTRAL',

		LIMPARDESENHOCARTORIAL: 'LIMPARDESENHOCARTORIAL',

		PONTONAINSERCAO: 'PONTONAINSERCAO',

		PONTONAINSERCAOEDICAO: 'PONTONAINSERCAOEDICAO',

		DESABILITARDESENHOCADASTRALEDICAO: 'DESABILITARDESENHOCADASTRALEDICAO',

		TIPODESENHOCADASTRAL: 'TIPODESENHOCADASTRAL',


		// ARQUIVO SHAPEFILE

    EVENTUPLOADSHAPEFILE: 'EVENTUPLOADSHAPEFILE',

		SHAPEFILECADASTRAL: 'SHAPEFILECADASTRAL',

		SHAPEFILECARTORIAL: 'SHAPEFILECARTORIAL',

		UPLOADSHAPEFILESUCCESSEDICAO: 'UPLOADSHAPEFILESUCCESSEDICAO',

		UPLOADSHAPEFILEDESCRICAOCARTORIALEDICAO: 'UPLOADSHAPEFILEDESCRICAOCARTORIALEDICAO',

		UPLOADSHAPEFILEDESCRICAOCADASTRALEDICAO: 'UPLOADSHAPEFILEDESCRICAOCADASTRALEDICAO',

		UPLOADSHAPEFILEEDICAO: 'UPLOADSHAPEFILEEDICAO',

		DELETEARQUIVOSHAPEFILE: 'DELETESHAPEFILECADASTRAL',

        DELETESHAPEFILECADASTRAL: 'DELETESHAPEFILECADASTRAL',

        DELETESHAPEFILECARTORIAL: 'DELETESHAPEFILECARTORIAL',


        //  BENFEITORIAS

        DESTACABENFEITORIAMAPA: 'DESTACABENFEITORIAMAPA',

        // PROPRIETÁRIO

        SETAR_UGS_ORGAOS: 'SETAR_UGS_ORGAOS',
        SETAR_PROPRIETARIO_FUSAO: 'SETAR_UGS_ORGAOS',
        LIMPAR_UGS_ORGAOS: 'LIMPAR_UGS_ORGAOS',

        // DADOS GERAIS DO IMÓVEL

        LIMPA_DADOS_RIPS_FUSAO: 'LIMPA_DADOS_RIPS_FUSAO',

        // UPDATE SIZE

        UPDATESIZEMAPA: 'UPDATESIZEMAPA',

        UPDATESIZEMAPAEDICAO: 'UPDATESIZEMAPAEDICAO',

        UPDATESIZEMAPADETALHAR: 'UPDATESIZEMAPADETALHAR',

        // MODAL VALIDACAO

        SHOWMODALVALIDACAOBENFEITORIA: 'SHOWMODALVALIDACAOBENFEITORIA',

        // RESULTADO PESQUISA

        RESULTADOPESQUISARIMOVEL: 'RESULTADOPESQUISARIMOVEL',

        //SELECIONAR RIPS MODAL RETORNA

        SELECIONARRIPSRETORNARSUSPENSAO: 'SELECIONARRIPSRETORNARSUSPENSAO',

        PREPARAMATRICULATRANSCRICAO: 'PREPARAMATRICULATRANSCRICAO',

        LIMPAR_ABA: 'limparAba',
        LIMPAR_ARQUIVO: 'limparArquivo',
        ADICIONAR_TITULO: 'adicionarTitulo',
        EDITAR_DOCUMENTO_UA: 'editarDocumentoUA'
	})


	.constant('TIPODESENHO',
	{
		SHAPEFILE: 1,

		VETORIZACAOEMTELA: 2,

		SHAPEFILEINCLUSAO: 222,

		VETORIZACAOEMTELAINCLUSAO: 333

	})

  .constant("ol", ol)

  .constant("USUARIO_LOGADO", "usuarioLogado")

  .constant("MENU", "menuAcessos")

  .constant("CADASTRO_IMOVEL", "CADASTRO DE IMÓVEIS")

  .constant("KEY_MENU_ACESSOS", "menuAcessos")

  .constant("KEY_USUARIO_LOGADO", "usuarioLogado")

  .constant('Enums', {

  	Action: {
  		ADD: 'ADD',
  		EDIT: 'EDIT',
  		DETAIL: 'DETAIL'
  	}

  })

  .constant('urlBase',  '/incorporacao/api/')

  .constant('ABA_LOCALIZACAO',  '-1')
  .constant('ABA_TERRENO',  '1')
  .constant('ABA_DADOS_GERAIS',  '2')
  .constant('ABA_UNIDADE_AUTONOMA',  '3')
  .constant('ABA_BENFEITORIA',  '4')
  .constant('ABA_PROPRIETARIO',  '5')
  .constant('CNPJ_UNIAO',  '00.489.828/0009-02')
  .constant('TIPO_PROPRIETARIO_UNIAO',  'União')
  ;

})();

(function () {
    'use strict';

    cfpLoadingBarProviderConfig.$inject = ["cfpLoadingBarProvider"];
    mdDateLocaleProviderConfig.$inject = ["$mdDateLocaleProvider"];
    mdThemingProviderConfig.$inject = ["$mdThemingProvider"];
    httpProviderConfig.$inject = ["$httpProvider"];
    mdDatepickerDirectiveConfig.$inject = ["$provide"];
    angular.module('su-cadastro-imovel')
        .config(cfpLoadingBarProviderConfig)
        .config(mdDateLocaleProviderConfig)
        .config(mdThemingProviderConfig)
        .config(httpProviderConfig)
        .config(mdDatepickerDirectiveConfig);

    function cfpLoadingBarProviderConfig(cfpLoadingBarProvider) {
        cfpLoadingBarProvider.includeBar = true;
    }

    function mdDateLocaleProviderConfig($mdDateLocaleProvider) {
        $mdDateLocaleProvider.firstDayOfWeek = 0;
    }

    function mdThemingProviderConfig($mdThemingProvider) {
        $mdThemingProvider.theme('default')
            .accentPalette('green', {
                'default': '500'
            });
        $mdThemingProvider.theme('error-toast');
        $mdThemingProvider.theme('success-toast');
    }

    function httpProviderConfig($httpProvider) {

        var regexData = /^\d{4}-\d{2}-\d{2}$/;

        function convertDateStringsToDates(input) {
            // Ignore things that aren't objects.
            if (!angular.isObject(input)) return input;

            for (var key in input) {
                if (!input.hasOwnProperty(key)) continue;

                var value = input[key];
                var match;
                // Check for string properties which look like dates.
                if (angular.isString(value) && (match = value.match(regexData))) {
                    // Assume that Date.parse can parse ISO 8601 strings, or has been shimmed in older browsers to do so.
                    var milliseconds = Date.parse(match[0] + " 00:00");
                    if (!isNaN(milliseconds)) {
                        input[key] = new Date(milliseconds);
                    }
                } else if (angular.isObject(value)) {
                    // Recurse into object
                    convertDateStringsToDates(value);
                }
            }
        }
        $httpProvider.defaults.transformResponse.push(function(responseData){
            convertDateStringsToDates(responseData);
            return responseData;
        });
    }

    function mdDatepickerDirectiveConfig($provide){

        $provide.decorator('mdDatepickerDirective', ["$delegate", function ($delegate) {
            var directive = $delegate[0];

            var template = directive.template;
            var link = directive.link;

            // extending the link function
            directive.compile = function () {
                return function (scope, element, attrs, ctrl) {

                    link.apply(this, arguments);
                    if (attrs.ngMask) {
                        var inputNgModelControl = scope.ctrl.ngInputElement.controller('ngModel');

                        // for the first initialization
                        inputNgModelControl.$formatters.push(function () {
                            var viewValue = scope.ctrl.ngInputElement.controller('mdDatepicker').ngModelCtrl.$viewValue;
                            if (!viewValue) {
                                return undefined;
                            }
                            return scope.ctrl.$mdDateLocale.formatDate(viewValue);
                        });

                        // for transfer validation
                        scope.$watch(function () {
                            return scope.ctrl.ngInputElement.controller('ngModel').$error.mask;
                        }, function (newValue) {
                            ctrl[0].$setValidity('mask', !newValue)
                        })
                    }
                };
            };
            // replacing template when needed
            directive.template = function (tElement, tAttrs) {
                var originalTemplate = template.apply(this, arguments);
                // adding mask and ng-model to mdDatepicker's input
                if (tAttrs.ngMask) {
                    var element = angular.element(originalTemplate);
                    element.find('input').attr('mask', tAttrs.ngMask);
                    element.find('input').attr('ng-model', "ctrl.dateInput");
                    element.find('input').attr('restrict', tAttrs.restrict);

                    // new tempalte with mask and ng-model attributes
                    var newTemplate =  '';
                    for (var i = 0; i < element.length; i++) {
                        newTemplate += element[i].outerHTML
                    }
                    return newTemplate ;
                }
                return originalTemplate;
            };
            return $delegate;
        }]);

    }
})();

angular.module("su-cadastro-imovel").run(["$templateCache", function($templateCache) {$templateCache.put("scripts-cadastro-imovel/app/views/dialog-validacao.html","<md-dialog aria-label=\"Por favor, preencher os campos obrigatórios\" flex=40><form name=cancelarImovel ng-submit=incluir()><md-toolbar><div class=md-toolbar-tools><h2>Por favor, preencher os campos obrigatórios</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><ul><li ng-repeat=\"error in errorsForms track by $index\">{{error}}</li></ul></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/app/views/main.view.tmpl.html","<div id=loading-bar-spinner class=\"progress-circle-container display-none\"><div id=progress-circle class=spinner-icon></div></div><div flex layout=column ng-controller=\"MenuController as ul\"><div style=overflow:auto id=content-scroll><!-- Barra de navageção --><md-toolbar layout=row class=md-whiteframe-z3><!-- hide-gt-sm --><md-button class=menu ng-click=ul.toggleMenu() aria-label=\"Menu da Aplicação\"><ng-md-icon icon=menu style=fill:#fff size=38></ng-md-icon></md-button><div layout=column hide-xs layout-align=\"center center\"><img src=assets/img/logo-spu.png></div><div layout=row flex=75 layout-align=\"end center\"><a ui-sref=INCORPORACAO.home style=\"margin: 13px 10px 0 0\"><ng-md-icon icon=home style=fill:#fff size=35 alt=Home><md-tooltip md-direction=bottom>Página inicial</md-tooltip></ng-md-icon></a><ng-md-icon icon=notifications style=\"fill:#fff; margin: 13px 10px 0 0\" size=35 alt=Notificações><md-tooltip md-direction=bottom>Notificações</md-tooltip></ng-md-icon><ng-md-icon icon=help style=\"fill:#fff; margin: 13px 10px 0 0\" size=35 alt=Ajuda><md-tooltip md-direction=bottom>Ajuda</md-tooltip></ng-md-icon><md-button class=\"md-fab md-primary md-hue-2\" ng-click=$mdOpenMenu($event) aria-label=Configurações><md-tooltip md-direction=left>Configurações da aplicação</md-tooltip><md-icon>person</md-icon></md-button></div></md-toolbar><div layout=row class=md-whiteframe-z3 style=\"background: #38813C !important\"><!-- BREADCRUMB --><div layout=row layout-align=\"start center\" flex=85 ncy-breadcrumb></div><div layout=row layout-align=\"end center\" style=\"color: #fff; font-size: 10px\">Sua sessão expira em 00:40:00</div><!-- Container Menu e Conteúdo --></div><!-- MENU --><menu></menu><!-- Conteúdo Principal --><md-content class=conteudo-principal flex layout=column><!-- TODO: usar o GlobalController no view state --><div ng-controller=\"GlobalController as vm\" ui-view flex=noshrink></div></md-content><footer class=footer style=\"bottom:0; position: fixed; z-index: 21\">Secretaria de Patrimônio da União - SPU | Esplanada dos Ministérios - Bloco C - 2º Andar Brasília-DF</footer></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/modal-fotos/modal-fotos.html","<md-dialog aria-label=Fotos flex=50><md-toolbar><div class=md-toolbar-tools><h2>{{ modFtCtrl.tituloModal }}</h2><span flex></span><md-button class=md-icon-button ng-click=modFtCtrl.fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><inc-carousel imagens=modFtCtrl.fotos src=false></inc-carousel></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/app/components/carousel/carousel.html","<div layout=column ng-show=\"imagens.length > 0\" class=carousel><div flex=100 layout=row><div flex=15 class=\"item volta\" layout=row layout-align=\"end center\"><md-button ng-disabled=\"selectedIndex==0\" ng-click=retorna() class=md-icon-button aria-label=Anterior><ng-md-icon icon=navigate_before size=30></ng-md-icon></md-button></div><div ng-show=src align=center flex=70 class=imagem><img ng-class=transition ng-src=data:image/png;base64,{{imagens[selectedIndex].imagem.arquivo}} ng-click=acessaImagem($event)></div><div ng-show=!src align=center flex=70 class=imagem ng-click=acessaImagem($event)><img style=\"max-width: 100% !important\" ng-class=transition ng-src=data:image/png;base64,{{imagens[selectedIndex].arquivo}}></div><div flex=15 class=\"item avanca\" layout=row layout-align=\"end center\"><md-button ng-disabled=\"selectedIndex==imagens.length-1\" ng-click=avanca() class=md-icon-button aria-label=Próximo><ng-md-icon icon=navigate_next size=30></ng-md-icon></md-button></div></div><div ng-show=src layout=row flex class=bolas><div align=center ng-repeat=\"i in imagens track by $index\"><ng-md-icon icon=\"{{$index==selectedIndex ? \'lens\':\'panorama_fisheye\'}}\" size=10></ng-md-icon></div></div><div ng-show=\"src != undefined && !src\" layout=row flex class=bolas style=\"margin: 0 auto !important\"><div align=center ng-repeat=\"i in imagens track by $index\"><ng-md-icon icon=\"{{$index==selectedIndex ? \'lens\':\'panorama_fisheye\'}}\" size=10></ng-md-icon></div></div><div ng-show=\"src != undefined && !src\" layout=row flex=100><div layout=column style=\"margin-top: 20px; margin-left: auto; margin-right: auto\"><span><label class=fonteNegrito>Descrição:</label><span class=span-original-component>{{ imagens[selectedIndex].descricao }}</span><br></span><span><label class=fonteNegrito>Data:</label><span class=span-original-component>{{ imagens[selectedIndex].data | date: \"dd/MM/yyyy\"}}</span><br></span></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/components/limitText/limitText.html","<div><div class=\"container-limit-texto {{show ? \'show-limit-texto\':\'\' }}\"><span ng-bind=texto></span></div><span ng-show=\"show==false\" class=mostrar-mais-limit-texto style=color:blue ng-click=\"show = true\">Mostrar mais</span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/album-fotos.html","<style type=text/css>.md-datepicker-button {\n		margin-left: -9px !important;\n	}</style><div layout=column><md-button class=\"md-primary md-raised\" ng-if=!isDetail ng-hide=\"selectedIndex == 1\" ng-click=habilitarUpload() aria-label=\"upload foto\">Adicionar foto</md-button><inc-carousel imagens=vm.benfeitoria.imagens src=true></inc-carousel><table ng-show=\"vm.benfeitoria.imagens.length>0\" class=mui-table style=\"width: 100% !important\" flex><thead><div class=barra-separadora ng-show=\"vm.benfeitoria.imagens.length>0\"></div></thead><tbody><tr ng-repeat=\"img in vm.benfeitoria.imagens\" ng-model-options=\"{trackBy: \'$value.id\'}\" style=\"text-align: center\"><td>{{img.nomeArquivo}}</td><td>{{img.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=removerImagemBenfeitoria($index) ng-if=!vm.isDetail></ng-md-icon></td></tr></tbody></table></div>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/cadastro-geometria.html","<div layout=column><div layout-gt-xs=row style=\"margin-bottom: 10px\"><md-radio-group class=secaoCadastroGeometriaBenfeitoria ng-model=benfeitoria.tipoArquivo style=\"float: left; margin-top: 10px\"><md-radio-button id=radio1 ng-value=1 style=float:left ng-click=habilitarDesenho(1) ng-disabled=isDetail>Arquivo (Shapefile)</md-radio-button><md-radio-button id=radio2 ng-value=2 style=\"float:left; margin-top: 0; margin-left: 15px !important\" ng-click=habilitarDesenho(2) ng-disabled=isDetail>Vetorização em Tela</md-radio-button></md-radio-group></div><input ng-model=benfeitoria.file type=file style=\"margin-bottom: 25px\" ng-disabled=\"benfeitoria.tipoArquivo === 2 || isDetail\" onchange=angular.element(this).scope().uploadArquivoBenfeitoria() id=fileBenfeitoria></div>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/dados-acessao-benfeitoria.html","<div layout=column><div layout=row><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.tipoAcessaoBenfeitoria.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" flex><label>Tipo Acessão/Benfeitoria</label><md-select ng-class=\"\" ng-model=vm.benfeitoria.tipoBenfeitoria ng-required=true class=secaoDadosAcessaoBenfeitoria ng-class=alterados.tipoBenfeitoria ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoAcessaoBenfeitoria ng-disabled=isDetail><md-option ng-repeat=\"item in tipoBenfeitoria\" ng-value=item ng-selected=\"item.id === isEdificacao\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.tipoEspecializacaoEdificacao.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\" flex><label>Especializacao Acessão/Benfeitoria</label><md-select ng-model=vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao ng-model-options=\"{trackBy: \'$value.id\'}\" class=secaoDadosAcessaoBenfeitoria ng-class=alterados.tipoEspecializacaoEdificacao ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1\" name=tipoEspecializacaoEdificacao ng-disabled=isDetail><md-option ng-repeat=\"item in tipoEspecializacaoEdificacao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.tipoEspecializacaoComplementar.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 2\" flex><label>Especialização Acessão/Benfeitoria</label><md-select ng-model=vm.benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 2\" name=tipoEspecializacaoComplementar class=secaoDadosAcessaoBenfeitoria ng-class=alterados.tipoEspecializacaoComplementar md-on-open=vm.getTipoEspecializacaoComplementar() ng-disabled=isDetail><md-option ng-repeat=\"item in tipoEspecializacaoComplementar\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\"><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.nome.$error.required && submittedBenfeitoria?\'md-input-invalid md-input-focused\':null\" class=md-block flex><label>Nome</label><input ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1\" name=nome type=text ng-model=vm.benfeitoria.nome class=secaoDadosAcessaoBenfeitoria ng-class=alterados.nome ng-disabled=isDetail></md-input-container></div><div layout=row><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.areaConstruida.$error.required && submittedBenfeitoria?\'md-input-invalid md-input-focused\':null\" class=md-block flex-gt-xs ng-show=vm.benfeitoria.tipoBenfeitoria.id><label>Área construída(m²)</label><input type=text ng-model=vm.benfeitoria.areaConstruida ui-number-mask required name=areaConstruida class=secaoDadosAcessaoBenfeitoria ng-class=alterados.areaConstruida ng-disabled=isDetail></md-input-container><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.areaProjecao.$error.required && submittedBenfeitoria?\'md-input-invalid md-input-focused\':null\" class=md-block flex-gt-xs ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\"><label>Área projeção(m²)</label><input type=text ng-model=vm.benfeitoria.edificacao.areaProjecao ui-number-mask ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1\" name=areaProjecao class=secaoDadosAcessaoBenfeitoria ng-class=alterados.areaProjecao ng-disabled=isDetail></md-input-container><md-input-container class=md-block flex-gt-xs md-no-float=true ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\"><!--alterado de taxa de ocupação para Fração de Ocupação--><label>Fração de Ocupação</label><input type=text ng-model=vm.benfeitoria.edificacao.taxaOcupacao ui-number-mask ng-disabled=true class=secaoDadosAcessaoBenfeitoria></md-input-container><md-input-container ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\" md-no-float><label>Índice de aproveitamento</label><input type=text ng-model=vm.benfeitoria.edificacao.indiceAproveitamento ng-disabled=true ui-number-mask class=secaoDadosAcessaoBenfeitoria></md-input-container></div><div layout=row ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\"><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.numeroPavimentos.$error.required && submittedBenfeitoria?\'md-input-invalid md-input-focused\':null\" flex><label>Número de pavimentos</label><input ng-model=vm.benfeitoria.edificacao.numeroPavimentos type=number class=secaoDadosAcessaoBenfeitoria ng-class=alterados.numeroPavimentos ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1\" name=numeroPavimentos ng-disabled=isDetail></md-input-container><md-input-container flex ng-class=\"forms.adicionarBenfeitoriaForm.numeroDormitorios.$error.required && submittedBenfeitoria?\'md-input-invalid md-input-focused\':null\" ng-show=\"vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 1 || vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 3 || vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 5\"><label>Número de dormitórios</label><input type=number ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 1 || vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 3 || vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 5\" ng-model=vm.benfeitoria.edificacao.numeroDormitorios name=numeroDormitorios class=secaoDadosAcessaoBenfeitoria ng-class=alterados.numeroDormitorios ng-class=alterados.numeroDormitorios ng-disabled=isDetail></md-input-container><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.numeroUnidadesResidenciais.$error.required && submittedBenfeitoria?\'md-input-invalid md-input-focused\':null\" ng-show=\"vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 3\" flex><label>Número de unidades residenciais</label><input type=text ng-model=vm.benfeitoria.edificacao.numeroUnidadesResidenciais ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 3\" name=numeroUnidadesResidenciais class=secaoDadosAcessaoBenfeitoria ng-class=alterados.numeroUnidadesResidenciais ng-disabled=isDetail></md-input-container></div><div layout=row ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.matriculaIsPreenchida\"><md-input-container style=\"margin: 5px 0 !important\"><md-checkbox ng-model=vm.benfeitoria.edificacao.averbada class=secaoDadosAcessaoBenfeitoria ng-disabled=isDetail>Averbada na matrícula do imóvel</md-checkbox></md-input-container></div><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.averbada.$error.required && submittedBenfeitoria?\'md-input-invalid md-input-focused\':null\" ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.benfeitoria.edificacao.averbada\"><label>Descrição na averbação</label><input type=text ng-model=vm.benfeitoria.edificacao.descricaoAverbacao ng-required=vm.benfeitoria.edificacao.averbada name=averbada class=secaoDadosAcessaoBenfeitoria ng-class=alterados.descricaoAverbacao ng-disabled=isDetail></md-input-container><!--<div layout=\"row\" ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\">--><!--<md-input-container style=\"margin: 5px 0 !important;\">--><!--<md-checkbox ng-class=\"alterados.possuiHabitese\" ng-model=\"vm.benfeitoria.edificacao.possuiHabitese\">--><!--Possui Habite-se--><!--</md-checkbox>--><!--</md-input-container>--><!--</div>--><!--<md-input-container ng-show=\"vm.benfeitoria.edificacao.possuiHabitese && vm.benfeitoria.tipoBenfeitoria.id === 1\"--><!--ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\">--><!--<label>Ano Habite-se</label>--><!--<input type=\"number\" ng-model=\"vm.benfeitoria.edificacao.anoHabitese\" ng-required=\"vm.benfeitoria.edificacao.possuiHabitese\" name=\"anoHabitese\"--><!--ng-class=\"alterados.anoHabitese\" maxlength=\"4\">--><!--<span class=\"form-error\" ng-show=\"forms.adicionarBenfeitoriaForm.anoHabitese.$error.required && submittedBenfeitoria\">Campo de preenchimento obrigatório</span>--><!--</md-input-container>--><div layout=row ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\"><md-input-container style=\"margin: 5px 0 !important\"><md-checkbox ng-class=alterados.possuiGaragem class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.edificacao.possuiGaragem ng-disabled=isDetail>Possui garagem</md-checkbox></md-input-container></div><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.numeroVagas.$error.required && submittedBenfeitoria?\'md-input-invalid md-input-focused\':null\" ng-show=vm.benfeitoria.edificacao.possuiGaragem ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\"><label>Número de vagas</label><input type=number class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.edificacao.numeroVagas ng-class=alterados.numeroVagas ng-required=vm.benfeitoria.edificacao.possuiGaragem name=numeroVagas ng-disabled=isDetail></md-input-container><div layout=row ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.benfeitoria.edificacao.numeroPavimentos >= 2\"><md-input-container style=\"margin: 5px 0 !important\"><md-checkbox ng-class=alterados.possuiElevador class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.edificacao.possuiElevador ng-disabled=isDetail>Possui elevador</md-checkbox></md-input-container></div><div layout=row ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\"><md-input-container style=\"margin: 5px 0 !important\"><md-checkbox ng-class=alterados.possuiENCE class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.edificacao.possuiENCE ng-disabled=isDetail>Possui Etiqueta Nacional de Conservação de Energia(ENCE) Geral</md-checkbox></md-input-container></div><div layout=row ng-class=\"forms.adicionarBenfeitoriaForm.tipoEnce.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.benfeitoria.edificacao.possuiENCE\"><md-input-container flex><label>Tipo</label><md-select class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.edificacao.tipoEnce ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.benfeitoria.edificacao.possuiENCE\" name=tipoEnce ng-disabled=isDetail><md-option ng-repeat=\"item in tipoEnce | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.tipoEnceParcial.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" ng-show=\"vm.benfeitoria.edificacao.tipoEnce.id === 2\" flex><label>Tipo de Parcial</label><md-select class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.edificacao.tipoEnceParcial ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"vm.benfeitoria.edificacao.tipoEnce.id === 2 && vm.benfeitoria.edificacao.possuiENCE\" name=tipoEnceParcial ng-disabled=isDetail><md-option ng-repeat=\"item in tipoEnceParcial | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.nivelEnce.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" ng-show=\"vm.benfeitoria.edificacao.tipoEnce.id === 1\" flex><label style=\"width: 1px\">Nível</label><md-select class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.edificacao.nivelEnce ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.benfeitoria.edificacao.possuiENCE\" name=nivelEnce ng-style=myObj ng-disabled=isDetail><md-option ng-repeat=\"item in nivelEnce | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.nivelEnce.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" ng-show=\"vm.benfeitoria.edificacao.tipoEnce.id === 2\" flex=10><label style=\"width: 15px\">Nível</label><md-select class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.edificacao.nivelEnce ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.benfeitoria.edificacao.possuiENCE\" name=nivelEnce ng-style=myObj ng-disabled=isDetail><md-option ng-repeat=\"item in nivelEnce\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.materialConstrucao.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 2\" flex><label>Material de construção</label><md-select class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.benfeitoriaComplementar.materialConstrucao ng-class=alterados.materialConstrucao ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 2\" ng-model-options=\"{trackBy: \'$value.id\'}\" name=materialConstrucao ng-disabled=isDetail><md-option ng-repeat=\"item in materialConstrucao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.estadoConservacao.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" ng-show=vm.benfeitoria.tipoBenfeitoria.id flex><label>Estado de conservação</label><md-select class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.estadoConservacao ng-class=alterados.estadoConservacao ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" name=estadoConservacao md-on-open=vm.getEstadoConservacao() ng-disabled=isDetail><md-option ng-repeat=\"item in estadoConservacao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.tipoEstrutura.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\" flex><label>Tipo de estrutura</label><md-select multiple class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.edificacao.tipoEstrutura ng-class=alterados.tipoEstrutura ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1\" ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoEstrutura md-on-open=vm.getTipoEstrutura() ng-disabled=isDetail><md-option ng-repeat=\"item in tipoEstrutura\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.padraoAcabamento.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\" ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1\" flex><label>Padrão de acabamento</label><md-select class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.edificacao.padraoAcabamento ng-class=alterados.padraoAcabamento ng-required=\"vm.benfeitoria.tipoBenfeitoria.id === 1\" ng-model-options=\"{trackBy: \'$value.id\'}\" md-on-open=vm.getPadraoAcabamento() name=padraoAcabamento ng-disabled=isDetail><md-option ng-repeat=\"item in padraoAcabamento\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><md-input-container ng-show=\"vm.benfeitoria.tipoBenfeitoria.id == 2\"><label>Observações gerais</label><textarea ng-class=alterados.observacoes class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.observacoes md-maxlength=1000 ng-disabled=isDetail></textarea></md-input-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/dados-acessibilidade.html","<div layout=column><md-input-container><md-checkbox ng-class=alterados.semInformacaoAcessibilidade ng-model=vm.benfeitoria.edificacao.semInformacaoAcessibilidade ng-disabled=isDetail>Sem informação de acessibilidade</md-checkbox></md-input-container><label>dados-acessibilidade1 - Há vagas próximas dos acessos de circulação de pedrestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente:</label><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.vagasSinalizadas.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\"><md-select ng-model=vm.benfeitoria.edificacao.vagasSinalizadas placeholder=\"Selecione uma opção\" style=\"width: 290px\" class=secaoCondicoesAcessibilidadeBenfeitoria ng-class=alterados.vagasSinalizadas ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!vm.benfeitoria.edificacao.semInformacaoAcessibilidade && vm.benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"vm.benfeitoria.edificacao.semInformacaoAcessibilidade || isDetail\" name=vagasSinalizadas><md-option ng-repeat=\"item in condicaoAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><label>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida:</label><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.acessoLivreObstaculos.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\"><md-select ng-model=vm.benfeitoria.edificacao.acessoLivreObstaculos placeholder=\"Selecione uma opção\" style=\"width: 290px\" class=secaoCondicoesAcessibilidadeBenfeitoria ng-class=alterados.acessoLivreObstaculos ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!vm.benfeitoria.edificacao.semInformacaoAcessibilidade && vm.benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"vm.benfeitoria.edificacao.semInformacaoAcessibilidade || isDetail\" name=acessoLivreObstaculos><md-option ng-repeat=\"item in condicaoAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><label>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida:</label><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.banheiroAcessivel.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\"><md-select ng-model=vm.benfeitoria.edificacao.banheiroAcessivel placeholder=\"Selecione uma opção\" style=\"width: 290px\" class=secaoCondicoesAcessibilidadeBenfeitoria ng-class=alterados.banheiroAcessivel ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!vm.benfeitoria.edificacao.semInformacaoAcessibilidade && vm.benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"vm.benfeitoria.edificacao.semInformacaoAcessibilidade || isDetail\" name=banheiroAcessivel><md-option ng-repeat=\"item in condicaoAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div ng-show=vm.benfeitoria.edificacao.possuiElevador><label>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida:</label><md-input-container ng-class=\"forms.adicionarBenfeitoriaForm.elevadorAcessivel.$error.required && submittedBenfeitoria?\'md-input-invalid\':null\"><md-select ng-model=vm.benfeitoria.edificacao.elevadorAcessivel placeholder=\"Selecione uma opção\" style=\"width: 290px\" class=secaoCondicoesAcessibilidadeBenfeitoria ng-class=elevadorAcessivel ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"vm.benfeitoria.edificacao.semInformacaoAcessibilidade || isDetail\" ng-required=\"vm.benfeitoria.edificacao.possuiElevador && !vm.benfeitoria.edificacao.semInformacaoAcessibilidade && vm.benfeitoria.tipoBenfeitoria.id === 1\" name=elevadorAcessivel><md-option ng-repeat=\"item in condicaoAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/dados-planta-benfeitoria.html","<ng-form novalidade name=forms.plantaBenfeitoria><div layout-gt-sm=column flex layout-margin><div layout-gt-sm=row flex><input type=file ngf-select=selectArquivo($files) ng-model=vm.planta.planta accept=\".pdf, .png, .jpg\" ngf-max-size=10MB ngf-model-invalid=errorFile ng-required=addPlanta name=planta id=input-file-id ng-disabled=isDetail></div><div layout-gt-sm=row flex><md-input-container class=md-block flex-gt-sm><label>Descrição</label><input ng-model=vm.planta.nome type=text ng-required=addPlanta name=nome ng-disabled=isDetail> <span class=form-error ng-show=uploadCertidaoCartorial.nome.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-datepicker ng-mask=39/19/9999 restrict=reject class=special ng-model=vm.planta.data md-placeholder=\"Data do documento\" md-max-date=hoje ng-required=addPlanta name=data style=\"margin: 13px 0 0 10px\" ng-disabled=isDetail></md-datepicker><span class=form-error ng-show=uploadCertidaoCartorial.nome.$error.required>Campo de preenchimento obrigatório</span></div><div layout=row layout-align=\"end right\"><md-button ng-click=cancelarUpload() class=\"md-raised md-primary\">Cancelar</md-button><md-button ng-disabled=forms.plantaBenfeitoria.$invalid ng-click=adicionaPlantaBenfeitoria() md-no-ink class=\"md-raised md-primary\" ng-disabled=isDetail>Incluir Arquivo de Planta</md-button></div></div></ng-form>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/dialog-adicionar-benfeitoria.html","<md-dialog aria-label=\"Incluir dados da benfeitoria\" flex=90 ng-cloak ng-controller=\"BenfeitoriaController as vm\"><md-toolbar><div class=md-toolbar-tools><h2>Incluir dados da benfeitoria</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-xs=column><div layout=column flex=30 flex-xs=100><div id=mapaInclude ng-if=vm.includeMap><ci-mapa-incorporacao showminimapa=false isbenfeitoria=true imovel=imovel action=add benfeitoria=vm.benfeitoria habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/benfeitoria/view/album-fotos.html\'\"></div></fieldset></div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Plantas da benfeitoria</legend><md-button ng-if=!isDetail ng-click=habilitaPlanta() class=\"md-raised md-primary\" style=\"width: 94%\">Adicionar Planta</md-button><md-table-container style=\"padding: 0px 7px\" ng-show=\"vm.benfeitoria.plantas.length > 0\"><table md-table ng-show=\"vm.benfeitoria.plantas.length > 0\"><thead><div class=barra-separadora></div></thead><tbody md-body><tr md-row ng-repeat=\"planta in vm.benfeitoria.plantas\" style=\"text-align: center\"><td flex=80 style=\"text-align: center; width: 260px\">{{planta.nome}}</td><td flex=20 style=\"text-align: center\"><a ng-click=removerPlantaBenfeitoria($index) ng-if=!vm.isDetail><ng-md-icon style=fill:#788489 icon=delete size=22><md-tooltip>Excluir Planta</md-tooltip></ng-md-icon></a><a ng-click=openPdfPlanta(planta)><ng-md-icon style=fill:#788489 icon=search size=22><md-tooltip>Visualizar Planta</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></fieldset></div><div layout=column flex=70 flex-xs=100 style=\"padding: 0 10px\"><ng-form name=forms.adicionarBenfeitoriaForm><v-accordion class=vAccordion--default style=\"margin-top: 20px\" multiple><v-pane ng-show=\"!addFoto && !addPlanta\"><v-pane-header ng-click=\"vm.validaSecao($event, forms.adicionarBenfeitoriaForm, 2, \'.secaoCadastroGeometriaBenfeitoria\')\">Cadastro de Geometria</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/benfeitoria/view/cadastro-geometria.html\'\"></div></v-pane-content></v-pane><v-pane expanded ng-show=\"!addFoto && !addPlanta\"><v-pane-header ng-click=\"vm.validaSecao($event, forms.adicionarBenfeitoriaForm, 2, \'.secaoDadosAcessaoBenfeitoria\')\">Dados da Acessão/Benfeitoria</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/benfeitoria/view/dados-acessao-benfeitoria.html\'\"></div></v-pane-content></v-pane><v-pane ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.benfeitoria.tipoBenfeitoria.id !== undefined && !addFoto && !addPlanta\"><v-pane-header ng-click=\"vm.validaSecao($event, forms.adicionarBenfeitoriaForm, 2, \'.secaoCondicoesAcessibilidadeBenfeitoria\')\">Condições de Acessibilidade</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/benfeitoria/view/dados-acessibilidade.html\'\"></div></v-pane-content></v-pane><v-pane ng-show=\"vm.benfeitoria.tipoBenfeitoria.id === 1 && vm.benfeitoria.tipoBenfeitoria.id !== undefined && !addFoto && !addPlanta\"><v-pane-header ng-click=\"vm.validaSecao($event, forms.adicionarBenfeitoriaForm, 2, \'.secaoDocumentacaoBenfeitoria\')\">Documentação</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/benfeitoria/view/documentacao-benfeitoria.html\'\"></div></v-pane-content></v-pane><div layout=row><md-input-container ng-show=\"vm.benfeitoria.tipoBenfeitoria.id == 1\" style=\"width: 100%\"><label>Observações gerais</label><textarea ng-class=alterados.observacoes class=secaoDadosAcessaoBenfeitoria ng-model=vm.benfeitoria.observacoes md-maxlength=1000 ng-disabled=isDetail></textarea></md-input-container></div><v-pane ng-show=addFoto expanded><v-pane-header ng-click=\"vm.validaSecao($event, forms.imagemBenfeitoriaForm, 2, \'.secaoUploadFotosBenfeitoria\')\">Upload de fotos da benfeitoria</v-pane-header><v-pane-content><form name=forms.imagemBenfeitoriaForm><div layout=column flex><div layout=row flex><input type=file ngf-select ng-model=imagem.foto accept=\".pdf, .png, .jpg\" ngf-max-size=5MB name=foto ng-required=addFoto ng-disabled=isDetail> <span class=form-error ng-show=\"forms.imagemBenfeitoriaForm.foto.$error.required && submittedFoto\">Campo de preenchimento obrigatório</span></div><div layout=row flex style=\"margin-top: 15px\"><md-input-container class=md-block ng-class=\"forms.imagemBenfeitoriaForm.descricao.$error.required && submittedFoto?\'md-input-invalid\':null\" flex-gt-sm><label>Descrição</label><input ng-model=imagem.descricao type=text name=descricao ng-required=addFoto ng-disabled=isDetail></md-input-container><md-datepicker ng-mask=39/19/9999 restrict=reject class=special ng-model=imagem.dataEmissao md-placeholder=\"Data da foto\" md-max-date=hoje name=dataEmissao style=\"margin: 13px 0 0 10px\" ng-required=addFoto ng-disabled=isDetail></md-datepicker><span class=form-error ng-show=\"forms.imagemBenfeitoriaForm.dataEmissao.$error.required && submittedFoto\">Campo de preenchimento obrigatório</span></div><div layout=row layout-align=\"end right\" style=\"margin-top: 20px\"><md-button ng-click=cancelar() class=md-raised ng-disabled=isDetail>Cancelar</md-button><md-button class=\"md-raised md-primary\" ng-click=upload() ng-disabled=isDetail>Incluir foto</md-button></div></div></form></v-pane-content></v-pane><v-pane ng-show=addPlanta expanded><v-pane-header>Upload de Arquivos</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/benfeitoria/view/dados-planta-benfeitoria.html\'\"></div></v-pane-content></v-pane></v-accordion><div layout=column><div layout=row layout-align=\"end right\" ng-show=\"!addFoto && !addPlanta\"><md-button class=md-raised ng-click=limpar() ng-disabled=isDetail>Limpar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=validarCampos() ng-disabled=isDetail>Incluir benfeitoria</md-button></div></div></ng-form></div></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/dialog-benfeitorias-canceladas.html","<md-dialog aria-label=\"Benfeitorias canceladas\" flex=60 ng-cloak><form name=benfeitoriasCanceladas><md-toolbar><div class=md-toolbar-tools><h2>Benfeitorias canceladas</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><p ng-show=\"benfeitorias.length <= 0\">Não existe benfeitoria cancelada para este registro.</p><md-table-container ng-show=\"benfeitorias.length > 0\"><table md-table><thead md-head><tr md-row><th md-column>Benfeitoria</th><th md-column>Detalhes</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in benfeitorias | limitTo: paginacao.limit: (paginacao.page - 1) * paginacao.limit | orderBy: \'dataCancelamento\'\" ng-model-options=\"{trackBy: \'$value.id\'}\"><td md-cell><img ng-src=\"{{item.imagens[0].imagem != undefined ? \'data:image/jpeg;base64,\'+item.imagens[0].imagem :\'./assets/img/no-image.png\'}}\" style=\"width: 175px; height: 150px\"></td><td md-cell><p><span style=\"font-weight: bold\">ID:</span> {{item.codigo}}</p><p ng-hide=\"item.edificacao === null || item.edificacao === undefined\"><span style=\"font-weight: bold\">Tipo de Acessão/Benfeitoria:</span> {{item.edificacao.tipoEspecializacaoEdificacao.descricao}}</p><p ng-hide=\"item.benfeitoriaComplementar === null || item.benfeitoriaComplementar === undefined\"><span style=\"font-weight: bold\">Tipo de Acessão/Benfeitoria:</span> {{item.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao}}</p><p><span style=\"font-weight: bold\">Nome:</span> {{item.nome}}</p><p><span style=\"font-weight: bold\">Área Construída(m²):</span> {{item.areaConstruida | number}}</p><p><span style=\"font-weight: bold\">Motivo do cancelamento:</span> {{item.motivoCancelamento}}</p><p><span style=\"font-weight: bold\">Número do processo:</span> {{item.numeroProcessoCancelamento}}</p><p><span style=\"font-weight: bold\">Data do cancelamento:</span> {{item.dataCancelamento | date : \'dd/MM/yyyy\'}}</p></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacao.limit md-limit-options=paginacao.limitOptions md-page=paginacao.page md-total=\"{{ benfeitorias.length }}\" md-page-select ng-disabled=\"benfeitorias.length < 6\"></md-table-pagination></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/dialog-cancelar-benfeitoria.html","<md-dialog aria-label=\"Cancelar benfeitoria\" flex=60 ng-cloak><ng-form name=cancelar><md-toolbar><div class=md-toolbar-tools><h2>Cancelar benfeitoria</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=column flex><md-input-container class=md-block flex><label>Motivo do cancelamento</label><md-select ng-model=benfeitoriaCancelada.motivo aria-label=\"Motivo do cancelamento\" required name=motivo><md-option value=Erro>Erro</md-option><md-option value=Demolição>Demolição</md-option><md-option value=Outro>Outro</md-option></md-select><span class=form-error ng-show=cancelar.motivo.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"benfeitoriaCancelada.motivo === \'Outro\'\"><label>Descrição do motivo</label><textarea ng-model=benfeitoriaCancelada.descricao ng-required=\"benfeitoriaCancelada.motivo === \'Outro\'\" md-maxlength=50 maxlength=50 minlength=10></textarea></md-input-container><md-input-container class=md-block flex ng-show=\"benfeitoriaCancelada.motivo === \'Outro\' || benfeitoriaCancelada.motivo === \'Demolição\'\"><label>Número do Processo</label><input ng-model=benfeitoriaCancelada.numeroProcesso ng-show=\"benfeitoriaCancelada.motivo === \'Outro\' || benfeitoriaCancelada.motivo === \'Demolição\'\"></md-input-container></div><div layout=row layout-align=\"end right\"><md-button ng-disabled=cancelar.$invalid ng-click=cancelarBenfeitoria() class=\"md-primary md-raised\">Confirmar</md-button><md-button class=\"md-primary md-raised\" ng-click=fechar()>Cancelar</md-button></div></div></md-dialog-content></ng-form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/dialog-confirmar-edicao-benfeitoria.html","<md-dialog aria-label=\"Confirmar edição benfeitoria\" flex=60 ng-cloak><ng-form name=confirmar><md-toolbar><div class=md-toolbar-tools><h2>Confirmar edição de benfeitoria</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=column flex><md-input-container class=md-block flex><p>Os dados de benfeitoria serão sincronizados com SPIUnet, sobrepondo os dados existentes no RIP selecionado.</p><p>Caso selecionado um mesmo RIP para mais de uma benfeitoria, os dados sincronizados no SPIUnet serão concatenados.</p><p>Deseja prosseguir?</p></md-input-container></div><div layout=row layout-align=\"end right\"><md-button ng-click=salvar() class=\"md-primary md-raised\">Sim</md-button><md-button class=\"md-primary md-raised\" ng-click=fechar()>Selecionar outro RIP Utilização</md-button></div></div></md-dialog-content></ng-form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/documentacao-benfeitoria.html","<div layout=row><md-input-container><md-checkbox ng-class=alterados.planoProtecaoIncendio ng-model=vm.benfeitoria.edificacao.planoProtecaoIncendio aria-label=\"Plano de proteção contra incêndio\" ng-disabled=isDetail>Plano de proteção contra incêndio</md-checkbox></md-input-container><md-input-container><input ng-model=tmp.planoProtecaoIncendioFile type=file style=\"margin: -4px 0 0 15px\" ng-disabled=\"!vm.benfeitoria.edificacao.planoProtecaoIncendio || isDetail\" ng-required=vm.benfeitoria.edificacao.planoProtecaoIncendio name=planoProtecaoIncendioFile ngf-select accept=\".pdf, .jpg, .png, .jpeg\" ngf-max-size=10MB aria-label=\"Plano de proteção contra incêndio\" ng-change=\"file2blob(\'planoProtecaoIncendioFile\', tmp.planoProtecaoIncendioFile)\"> <span class=form-error ng-show=\"forms.adicionarBenfeitoriaForm.planoProtecaoIncendioFile.$error.required && submittedBenfeitoria\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"tmp.planoProtecaoIncendioFile != undefined\"><ng-md-icon ng-click=\"tmp.planoProtecaoIncendioFile = undefined\" class=icon-delete icon=delete ng-disabled=isDetail></ng-md-icon></md-input-container></div><div layout=row><md-input-container><md-checkbox ng-class=alterados.habitese ng-model=vm.benfeitoria.edificacao.habitese aria-label=Habite-se ng-disabled=isDetail>Habite-se</md-checkbox></md-input-container><md-input-container ng-show=vm.benfeitoria.edificacao.habitese style=\"margin: 12px 0 0 20px\"><label>Ano*:</label><input ng-model=vm.benfeitoria.edificacao.anoFileHabitese type=number maxlength=4 name=ano mask-repeat=4 ng-required=vm.benfeitoria.edificacao.habitese maxlength=4 ng-disabled=isDetail> <span class=form-error ng-show=\"forms.adicionarBenfeitoriaForm.ano.$error.required && submittedBenfeitoria\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container><input ng-model=tmp.habiteseFile type=file style=\"margin: -4px 0 0 15px\" ng-disabled=\"!vm.benfeitoria.edificacao.habitese || isDetail\" ng-required=vm.benfeitoria.edificacao.habitese ngf-select accept=\".pdf, .jpg, .png, .jpeg\" ngf-max-size=10MB aria-label=Habite-se name=habitese ng-change=\"file2blob(\'habiteseFile\', tmp.habiteseFile)\"> <span class=form-error ng-show=\"forms.adicionarBenfeitoriaForm.habitese.$error.required && submittedBenfeitoria\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"tmp.habiteseFile != undefined\"><ng-md-icon ng-click=\"tmp.habiteseFile = undefined\" class=icon-delete icon=delete ng-disabled=isDetail></ng-md-icon></md-input-container></div><div layout=row><md-input-container><md-checkbox ng-class=alterados.alvaraConstrucao ng-model=vm.benfeitoria.edificacao.alvaraConstrucao aria-label=\"Alvará de construção/reforma\" ng-disabled=isDetail>Alvará de construção/reforma</md-checkbox></md-input-container><md-input-container><input ng-model=tmp.alvaraConstrucaoFile type=file ngf-select accept=\".pdf, .jpg, .png, .jpeg\" ngf-max-size=10MB style=\"margin: -4px 0 0 15px\" ng-disabled=\"!vm.benfeitoria.edificacao.alvaraConstrucao || isDetail\" ng-required=vm.benfeitoria.edificacao.alvaraConstrucao aria-label=\"Alvará de construção/reforma\" name=alvaraConstrucao ng-change=\"file2blob(\'alvaraConstrucaoFile\', tmp.alvaraConstrucaoFile)\"> <span class=form-error ng-show=\"forms.adicionarBenfeitoriaForm.alvaraConstrucao.$error.required && submittedBenfeitoria\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"tmp.alvaraConstrucaoFile != undefined\"><ng-md-icon ng-click=\"tmp.alvaraConstrucaoFile = undefined\" class=icon-delete icon=delete ng-disabled=isDetail></ng-md-icon></md-input-container></div><div layout=row><md-input-container><md-checkbox ng-class=alterados.laudoInspencao ng-model=vm.benfeitoria.edificacao.laudoInspencao aria-label=\"Laudo de inspeção predial\" ng-disabled=isDetail>Laudo de inspeção predial</md-checkbox></md-input-container><md-input-container><input ng-model=tmp.laudoInspencaoFile type=file ngf-select accept=\".pdf, .jpg, .png, .jpeg\" ngf-max-size=10MB style=\"margin: -4px 0 0 15px\" ng-disabled=\"!vm.benfeitoria.edificacao.laudoInspencao || isDetail\" ng-required=vm.benfeitoria.edificacao.laudoInspencao aria-label=\"Laudo de inspeção predial\" name=laudoInspencao ng-change=\"file2blob(\'laudoInspencaoFile\', tmp.laudoInspencaoFile)\"> <span class=form-error ng-show=\"forms.adicionarBenfeitoriaForm.laudoInspencao.$error.required && submittedBenfeitoria\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"tmp.laudoInspencaoFile != undefined\"><ng-md-icon ng-click=\"tmp.laudoInspencaoFile = undefined\" class=icon-delete icon=delete ng-disabled=isDetail></ng-md-icon></md-input-container></div><div layout-gt-xs=row><md-input-container><label>Outro:</label><textarea ng-model=vm.benfeitoria.edificacao.outroDescricao ng-disabled=isDetail></textarea></md-input-container><md-input-container><input class=md-raised style=\"margin: -4px 0 0 15px\" ngf-select ng-model=vm.benfeitoria.edificacao.outroStr ng-change=\"file2blob(\'outroFile\', vm.benfeitoria.edificacao.outroStr)\" type=file ng-disabled=\"!vm.benfeitoria.edificacao.outroDescricao || isDetail\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-required=\"vm.benfeitoria.edificacao.outroDescricao && vm.benfeitoria.edificacao.outroStr === undefined\" name=outroDescricaoFile aria-label=\"Outro Documento\"> <span class=form-error ng-show=\"forms.adicionarBenfeitoriaForm.outroDescricaoFile.$error.required && submittedBenfeitoria\">Campo de preenchimento obrigatório </span><span style=\"margin-left:140px; border-bottom:1px solid #000\" ng-if=\"vm.benfeitoria.edificacao.outroStr != undefined\">{{vm.benfeitoria.edificacao.outroDescricao}}</span></md-input-container><md-input-container ng-show=\"vm.benfeitoria.edificacao.outroStr != undefined\"><ng-md-icon ng-click=\"vm.benfeitoria.edificacao.outroStr = undefined\" class=icon-delete icon=delete ng-disabled=isDetail></ng-md-icon></md-input-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/incluir-dados-benfeitoria.html","<div layout=row layout-xs=column><div layout=column flex=30 style=\"padding: 0 10px\"><div layout=column><div style=\"border-bottom: 2px solid #4caf50; padding: 5px 0\">Localização da Acessão/Benfeitoria</div><div class=div-cimapa ng-if=imovelCtrl.isInclusaoBenfeitoria><ci-mapa-incorporacao showminimapa=false imovel=imovel action=add tabindex=4 benfeitoria=imovelCtrl.benfeitoria habilitar-desenho=\"imovelCtrl.tipoDesenhoBenfeitoria === 2\"></ci-mapa-incorporacao></div></div><!--<div layout=\"column\" ng-show=\"imovelCtrl.benfeitoria.imagens.length > 0\">\n            <div style=\"border-bottom: 2px solid #4CAF50; padding: 5px 0;\">\n                Álbum de Fotos\n            </div>\n\n            <inc-carousel imagens=\"imovelCtrl.benfeitoria.imagens\">/<inc-carousel>\n        </div>--></div><div layout=column flex=70 flex-xs=100 style=\"padding: 0 10px\"><ng-form name=imovelCtrl.forms.inclusaoBenfeitoriaForm><v-accordion class=vAccordion--default multiple><!-- Cadastro de Geometria --><v-pane expanded ng-if=!imovelCtrl.isBenfeitoriaComplementar()><v-pane-header ng-click=\"vm.validaSecao($event, forms.adicionarBenfeitoriaForm, 2, \'.secaoCadastroGeometriaBenfeitoria\')\">Cadastro de Geometria</v-pane-header><v-pane-content><div layout=column><div layout=column><md-radio-group class=secaoCadastroGeometriaBenfeitoria ng-model=imovelCtrl.tipoDesenhoBenfeitoria layout=row><md-radio-button ng-value=1 flex>Arquivo (Shapefile)</md-radio-button><md-radio-button ng-value=2 flex>Vetorização</md-radio-button></md-radio-group></div><ci-input-file desabilitado=\"imovelCtrl.tipoDesenhoBenfeitoria !== 1\" objeto=arquivoShape model=imovelCtrl.filesBenfeitoria.shapefile botao=exibirBotao cif-onchange=imovelCtrl.uploadShapefileBenfeitoria(arquivo) cif-onremove=imovelCtrl.limparUploadShapefileBenfeitoria()></ci-input-file></div></v-pane-content></v-pane><!-- Dados da Benfeitoria --><v-pane><v-pane-header ng-click=\"vm.validaSecao($event, forms.adicionarBenfeitoriaForm, 2, \'.secaoDadosAcessaoBenfeitoria\')\">Dados da Acessão/Benfeitoria</v-pane-header><v-pane-content><div layout=column><div layout=row><md-input-container flex><label>Tipo de Acessão/Benfeitoria</label><md-select ng-model=imovelCtrl.benfeitoria.tipoBenfeitoria ng-required=true class=secaoDadosAcessaoBenfeitoria ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoAcessaoBenfeitoria><md-option ng-repeat=\"item in imovelCtrl.tiposBenfeitoria | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-show=imovelCtrl.isBenfeitoriaEdificacao() flex><label id=tipoEspecializacaoEdificacaoLabel>Tipo de Especialização de Edificação</label><!--<md-select\n                    ng-model=\"imovelCtrl.benfeitoria.edificacao.tipoEspecializacaoEdificacao\"\n                    ng-model-options=\"{trackBy: \'$value.id\'}\"\n                    class=\"secaoDadosAcessaoBenfeitoria\"\n                    ng-required=\"imovelCtrl.isBenfeitoriaEdificacao()\"\n                    name=\"tipoEspecializacaoEdificacao\"\n                  >\n                    <md-option\n                      ng-repeat=\"item in imovelCtrl.tiposEspecializacaoEdificacao\"\n                      ng-value=\"item\"\n                    >\n                      {{item.descricao}}\n                    </md-option>\n                  </md-select>--><md-autocomplete campo-ajuda=INC_FORM_LOC_SISTEMA_REFERENCIA class=secaoDadosAcessaoBenfeitoria ng-required=imovelCtrl.isBenfeitoriaEdificacao() name=tipoEspecializacaoEdificacao md-clear-button=!$mdAutocompleteCtrl.isDisabled md-selected-item=imovelCtrl.benfeitoria.edificacao.tipoEspecializacaoEdificacao md-search-text=$scope.searchTipoEspecializacaoEdificacao md-items=\"item in imovelCtrl.tiposEspecializacaoEdificacao | filter:{descricao:$scope.searchTipoEspecializacaoEdificacao} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=tipoEspecializacaoEdificacaoLabel><md-item-template><span md-highlight-text=$scope.searchTipoEspecializacaoEdificacao md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchTipoEspecializacaoEdificacao}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container ng-show=imovelCtrl.isBenfeitoriaComplementar() flex><label id=tipoEspecializacaoComplementarLabel>Tipo de Especialização Complementar</label><!--<md-select\n                    ng-model=\"imovelCtrl.benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar\"\n                    ng-model-options=\"{trackBy: \'$value.id\'}\"\n                    ng-required=\"imovelCtrl.isBenfeitoriaComplementar()\"\n                    name=\"tipoEspecializacaoComplementar\"\n                    class=\"secaoDadosAcessaoBenfeitoria\"\n                  >\n                    <md-option\n                      ng-repeat=\"item in imovelCtrl.tiposEspecializacaoComplementar\"\n                      ng-value=\"item\"\n                    >\n                      {{item.descricao}}\n                    </md-option>\n                  </md-select>--><md-autocomplete campo-ajuda=INC_FORM_LOC_SISTEMA_REFERENCIA ng-required=imovelCtrl.isBenfeitoriaComplementar() name=tipoEspecializacaoComplementar class=secaoDadosAcessaoBenfeitoria md-clear-button=!$mdAutocompleteCtrl.isDisabled md-selected-item=imovelCtrl.benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar md-search-text=$scope.searchTipoEspecializacaoComplementar md-items=\"item in imovelCtrl.tiposEspecializacaoComplementar | filter:{descricao:$scope.searchTipoEspecializacaoComplementar} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=tipoEspecializacaoComplementarLabel><md-item-template><span md-highlight-text=$scope.searchTipoEspecializacaoComplementar md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchTipoEspecializacaoComplementar}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container></div><div layout=row ng-show=imovelCtrl.isBenfeitoriaEdificacao()><md-input-container class=md-block flex><label>Descrição</label><input ng-required=imovelCtrl.isBenfeitoriaEdificacao() name=nome type=text ng-model=imovelCtrl.benfeitoria.nome class=secaoDadosAcessaoBenfeitoria></md-input-container></div><div layout=row ng-show=imovelCtrl.benfeitoria.tipoBenfeitoria.id><md-input-container flex><label>Área Construída(m²)</label><input type=text ng-model=imovelCtrl.benfeitoria.areaConstruida ui-number-mask=\"imovelCtrl.isBenfeitoriaEdificacao() ? 2 : 4\" ng-required=imovelCtrl.isBenfeitoriaEdificacao() name=areaConstruida class=secaoDadosAcessaoBenfeitoria></md-input-container><md-input-container flex ng-show=imovelCtrl.isBenfeitoriaEdificacao()><label>Área de Projeção(m²)</label><input type=text ng-model=imovelCtrl.benfeitoria.edificacao.areaProjecao ui-number-mask ng-required=imovelCtrl.isBenfeitoriaEdificacao() name=areaProjecao class=secaoDadosAcessaoBenfeitoria></md-input-container></div><div layout=row ng-show=imovelCtrl.isBenfeitoriaEdificacao()><!--Alterado de taxa de ocupação para Fração de Ocupação--><md-input-container class=md-input-has-placeholder flex><label>Fração de Ocupação</label><div class=md-input disabled>{{imovelCtrl.getTaxaOcupacao() | number:2}}</div></md-input-container><md-input-container class=md-input-has-placeholder flex><label>Índice de Aproveitamento</label><div class=md-input disabled>{{imovelCtrl.getIndiceAproveitamento() | number:2}}</div></md-input-container></div><div layout=row ng-show=imovelCtrl.isBenfeitoriaEdificacao()><md-input-container flex><label>Número de Pavimentos</label><input ng-model=imovelCtrl.benfeitoria.edificacao.numeroPavimentos type=number class=secaoDadosAcessaoBenfeitoria ng-required=imovelCtrl.isNumeroPavimentosRequired() name=numeroPavimentos ng-disabled=imovelCtrl.isNumeroPavimentosDisabled()></md-input-container><md-input-container flex ng-show=\"imovelCtrl.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 1 || imovelCtrl.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 3 || imovelCtrl.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 5\"><label>Número de Dormitórios</label><input type=number ng-required=\"imovelCtrl.isBenfeitoriaEdificacao() && (imovelCtrl.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 1 || imovelCtrl.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 3 || imovelCtrl.benfeitoria.edificacao.tipoEspecializacaoEdificacao.id === 5)\" ng-model=imovelCtrl.benfeitoria.edificacao.numeroDormitorios name=numeroDormitorios class=secaoDadosAcessaoBenfeitoria></md-input-container></div><div layout=row ng-show=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.imovel.incorporacao.matriculas.length > 0\"><md-input-container><md-checkbox ng-model=imovelCtrl.benfeitoria.edificacao.averbada class=secaoDadosAcessaoBenfeitoria>Averbada na matrícula do imóvel</md-checkbox></md-input-container></div><md-input-container ng-show=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.imovel.incorporacao.matriculas.length > 0 && imovelCtrl.benfeitoria.edificacao.averbada\"><label>Descrição da Averbação</label><input type=text ng-model=imovelCtrl.benfeitoria.edificacao.descricaoAverbacao ng-required=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.imovel.incorporacao.matriculas.length > 0 && imovelCtrl.benfeitoria.edificacao.averbada\" name=averbada class=secaoDadosAcessaoBenfeitoria></md-input-container><div layout=column ng-show=imovelCtrl.isBenfeitoriaEdificacao()><div layout-margin>Possui garagem? *</div><md-radio-group ng-model=imovelCtrl.benfeitoria.edificacao.possuiGaragem><div layout=row style=\"margin: 0 20px\"><md-radio-button value=Sim flex>Sim</md-radio-button><md-radio-button value=Não flex>Não</md-radio-button><md-radio-button value=\"Sem Informação\" flex>Sem Informação</md-radio-button></div></md-radio-group></div><div layout=column ng-show=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.isBenfeitoriaComGaragem()\"><div layout-margin>Número de Vagas</div><div layout=row><md-input-container flex><label>Cobertas</label><input type=number class=secaoDadosAcessaoBenfeitoria ng-model=imovelCtrl.benfeitoria.edificacao.numeroVagasCobertas ng-required=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.isBenfeitoriaComGaragem()\" name=numeroVagasCobertas></md-input-container><md-input-container flex><label>Descobertas</label><input type=number class=secaoDadosAcessaoBenfeitoria ng-model=imovelCtrl.benfeitoria.edificacao.numeroVagasDescobertas ng-required=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.isBenfeitoriaComGaragem()\" name=numeroVagasDescobertas></md-input-container><div flex layout=row layout-align=\"center center\">Total = {{imovelCtrl.benfeitoria.edificacao.numeroVagasCobertas + imovelCtrl.benfeitoria.edificacao.numeroVagasDescobertas || 0}}</div></div></div><div layout=row ng-show=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.benfeitoria.edificacao.numeroPavimentos > 1\"><md-input-container><md-checkbox ng-class=alterados.possuiElevador class=secaoDadosAcessaoBenfeitoria ng-model=imovelCtrl.benfeitoria.edificacao.possuiElevador>Possui Elevador</md-checkbox></md-input-container></div><div layout=row ng-show=imovelCtrl.isBenfeitoriaEdificacao()><md-input-container><md-checkbox ng-class=alterados.possuiENCE class=secaoDadosAcessaoBenfeitoria ng-model=imovelCtrl.benfeitoria.edificacao.possuiENCE>Possui Etiqueta Nacional de Conservação de Energia (ENCE)</md-checkbox></md-input-container></div><div layout=row ng-show=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.benfeitoria.edificacao.possuiENCE\"><md-input-container flex><label>Tipo</label><md-select class=secaoDadosAcessaoBenfeitoria ng-model=imovelCtrl.benfeitoria.edificacao.tipoEnce ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.benfeitoria.edificacao.possuiENCE\" name=tipoEnce><md-option ng-repeat=\"item in imovelCtrl.tiposEnce | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-show=\"imovelCtrl.benfeitoria.edificacao.tipoEnce.id === 2\" flex><label>Tipo de Parcial</label><md-select class=secaoDadosAcessaoBenfeitoria ng-model=imovelCtrl.benfeitoria.edificacao.tipoEnceParcial ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.benfeitoria.edificacao.possuiENCE && imovelCtrl.benfeitoria.edificacao.tipoEnce.id === 2\" name=tipoEnceParcial><md-option ng-repeat=\"item in imovelCtrl.tiposEnceParcial | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex><label>Nível</label><md-select class=secaoDadosAcessaoBenfeitoria ng-model=imovelCtrl.benfeitoria.edificacao.nivelEnce ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"imovelCtrl.isBenfeitoriaEdificacao() && imovelCtrl.benfeitoria.edificacao.possuiENCE\" name=nivelEnce><md-option ng-repeat=\"item in imovelCtrl.niveisEnce | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><md-input-container ng-show=imovelCtrl.isBenfeitoriaComplementar()><label>Descrição/Observações</label><textarea ng-class=alterados.observacoes class=secaoDadosAcessaoBenfeitoria ng-model=imovelCtrl.benfeitoria.observacoes md-maxlength=1000></textarea></md-input-container></div></v-pane-content></v-pane><!-- Condições de Acessibilidade --><v-pane ng-show=imovelCtrl.benfeitoria.tipoBenfeitoria><v-pane-header ng-click=\"vm.validaSecao($event, forms.adicionarBenfeitoriaForm, 2, \'.secaoCondicoesAcessibilidadeBenfeitoria\')\">Condições de Acessibilidade</v-pane-header><v-pane-content><div layout=column><md-input-container><md-checkbox ng-class=alterados.semInformacaoAcessibilidade ng-model=imovelCtrl.benfeitoria.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade</md-checkbox></md-input-container><div layout=row><div>Possui Laudo de Acessibilidade?</div></div><div layout=row><md-input-container flex-gt-xs><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=imovelCtrl.benfeitoria.edificacao.possuiLaudo placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=existeLaudo ng-change=atualizarLinkLaudo(); ng-disabled=condicoesAcessibilidadeDisabled() name=possuiLaudo><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex-gt-xs><label>Link do Laudo de Acessibilidade</label><input ng-disabled=false type=text ng-model=imovelCtrl.benfeitoria.edificacao.linkLaudo name=linkLaudo ng-required=existeLaudo maxlength=150 md-maxlength=150></md-input-container></div><div layout=row><div>Possui Plano de Trabalho?</div></div><div layout=row><md-input-container flex-gt-xs><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=imovelCtrl.benfeitoria.edificacao.possuiPlanoTrabalho placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=existePlano ng-change=atualizarLinkPlano(); ng-disabled=condicoesAcessibilidadeDisabled() name=possuiPlanoTrabalho><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex-gt-xs><label>Link do Plano de Trabalho</label><input ng-disabled=false type=text ng-model=imovelCtrl.benfeitoria.edificacao.linkPlanoTrabalho ng-required=existePlano name=linkPlano md-maxlength=150 aria-label=\" Link do plano de trabalho\"></md-input-container></div><div>Vagas para Pessoa com Deficiência reservadas e sinalizadas perto do acesso (2% do total de vagas)</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=imovelCtrl.benfeitoria.edificacao.vagasDefFisicos placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=!benfeitoria.edificacao.semInformacaoAcessibilidade ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=vagasDefFisicos><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Vagas para Pessoa Idosa reservadas e sinalizadas perto do acesso (5% do total de vagas).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_IDOSOS ng-model=imovelCtrl.benfeitoria.edificacao.vagasIdosos placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=!benfeitoria.edificacao.semInformacaoAcessibilidade ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=vagasIdosos><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Rebaixamento de calçadas.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_REBAIXA_CALCADAS ng-model=imovelCtrl.benfeitoria.edificacao.calcadasRebaixa placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=!benfeitoria.edificacao.semInformacaoAcessibilidade ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=calcadasRebaixa><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><!-- <div ng-show=\"benfeitoria.edificacao.possuiElevador\"> --><div>Calçadas sem barreiras nas proximidades da edificação.</div><md-input-container><md-select campo-ajuda=\"INC_FORM_BENFEITORIA_CALCADAS\'\" ng-model=imovelCtrl.benfeitoria.edificacao.calcadasLivres placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=!benfeitoria.edificacao.semInformacaoAcessibilidade ng-disabled=benfeitoria.edificacao.semInformacaoAcessibilidade name=calcadasLivres ng-disabled=isDetalhar()><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Entrada livre de barreiras (catracas, porta giratória, trilhos não embutidos, degraus, entre outros).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_ENTRADA_LIVRE ng-model=imovelCtrl.benfeitoria.edificacao.entradaLivre placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=!benfeitoria.edificacao.semInformacaoAcessibilidade ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=entradaLivre><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Todos os pavimentos podem ser acessados por pessoa em cadeira de rodas.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_CADEIRA_RODAS ng-model=imovelCtrl.benfeitoria.edificacao.pavimentosLivreCadRodas placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=!benfeitoria.edificacao.semInformacaoAcessibilidade ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=pavimentosLivreCadRodas><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Balcão de informação acessível (com pelo menos um trecho rebaixado conforme norma técnica).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_INFORMACOES ng-model=imovelCtrl.benfeitoria.edificacao.balcaoInformacoes placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=!benfeitoria.edificacao.semInformacaoAcessibilidade ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=balcaoInformacoes><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Piso Tátil.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_PISO_TATIL ng-model=imovelCtrl.benfeitoria.edificacao.pisoTatil placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=pisoTatil><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Corredores com largura adequada e sem obstáculos.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_CORREDOR_LARGO ng-model=imovelCtrl.benfeitoria.edificacao.corredorLargo placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=corredorLargo><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Sanitário acessível de uso público.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_SANITARIO_PUBLICO ng-model=imovelCtrl.benfeitoria.edificacao.sanitarioPublico placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=sanitarioPublico><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Sinalização em braile e alto relevo junto a informações relevantes (ex. sanitários, elevadores).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_BRAILE ng-model=imovelCtrl.benfeitoria.edificacao.braile placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=braile><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Atendimento em LIBRAS.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_LIBRAS ng-model=imovelCtrl.benfeitoria.edificacao.libras placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=libras><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div></v-pane-content></v-pane></v-accordion></ng-form></div><!-- Documentação --><v-pane ng-show=imovelCtrl.isBenfeitoriaEdificacao()><v-pane-header ng-click=\"vm.validaSecao($event, forms.adicionarBenfeitoriaForm, 2, \'.secaoDocumentacaoBenfeitoria\')\">Documentação</v-pane-header><v-pane-content><div layout=row layout-align=\"start center\"><div layout=column flex><div>Plano de Proteção Contra Incêndio *</div><md-radio-group ng-required=true ng-model=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiPlanoProtecaoIncendio><div layout=row layout-margin><md-radio-button ng-value=true>Sim</md-radio-button><md-radio-button ng-value=false>Não</md-radio-button></div></md-radio-group></div><input type=file ngf-select ng-model=imovelCtrl.arquivosDocumentacaoBenfeitoria.planoProtecaoIncendioFile ng-required=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiPlanoProtecaoIncendio ng-disabled=!imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiPlanoProtecaoIncendio ng-change=imovelCtrl.uploadArquivoPlanoProtecaoIncendio() flex></div><div layout=row layout-align=\"start center\"><div layout=column flex><div>Habite-se *</div><md-radio-group ng-required=true ng-model=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiHabitese><div layout=row layout-margin><md-radio-button ng-value=true>Sim</md-radio-button><md-radio-button ng-value=false>Não</md-radio-button></div></md-radio-group></div><md-input-container flex><input type=number maxlength=4 placeholder=Ano ng-model=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.anoHabitese ng-required=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiHabitese ng-disabled=!imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiHabitese></md-input-container><input type=file ngf-select ng-model=imovelCtrl.arquivosDocumentacaoBenfeitoria.habiteseFile ng-required=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiHabitese ng-disabled=!imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiHabitese ng-change=imovelCtrl.uploadArquivoHabitese() flex=50></div><div layout=row layout-align=\"start center\"><div layout=column flex><div>Alvará de Construção/Reforma *</div><md-radio-group ng-required=true ng-model=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiAlvaraConstrucao><div layout=row layout-margin><md-radio-button ng-value=true>Sim</md-radio-button><md-radio-button ng-value=false>Não</md-radio-button></div></md-radio-group></div><input type=file ngf-select ng-model=imovelCtrl.arquivosDocumentacaoBenfeitoria.alvaraConstrucaoFile ng-required=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiAlvaraConstrucao ng-disabled=!imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiAlvaraConstrucao ng-change=imovelCtrl.uploadArquivoAlvaraConstrucao() flex></div><div layout=row layout-align=\"start center\"><div layout=column flex><div>Laudo de Inspeção Predial *</div><md-radio-group ng-required=true ng-model=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiLaudoInspecao><div layout=row layout-margin><md-radio-button ng-value=true>Sim</md-radio-button><md-radio-button ng-value=false>Não</md-radio-button></div></md-radio-group></div><input type=file ngf-select ng-model=imovelCtrl.arquivosDocumentacaoBenfeitoria.laudoInspecaoFile ng-required=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiLaudoInspecao ng-disabled=!imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiLaudoInspecao ng-change=imovelCtrl.uploadArquivoLaudoInspecao() flex></div><div layout=row layout-align=\"start center\"><div layout=column flex><div>Relatório de Acessibilidade *</div><md-radio-group ng-required=true ng-model=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiRelatorioAcessibilidade><div layout=row layout-margin><md-radio-button ng-value=true>Sim</md-radio-button><md-radio-button ng-value=false>Não</md-radio-button></div></md-radio-group></div><input type=file ngf-select ng-model=imovelCtrl.arquivosDocumentacaoBenfeitoria.relatorioAcessibilidadeFile ng-required=imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiRelatorioAcessibilidade ng-disabled=!imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.possuiRelatorioAcessibilidade ng-change=imovelCtrl.uploadArquivoRelatorioAcessibilidade() flex></div><div layout=row layout-align=\"start center\"><md-input-container flex><label>Outro</label><input type=text maxlength=150 ng-model=imovelCtrl.arquivosDocumentacaoBenfeitoria.outroDocumento.descricao></md-input-container><input type=file ngf-select ng-model=imovelCtrl.arquivosDocumentacaoBenfeitoria.outroDocumento.arquivo ng-required=imovelCtrl.arquivosDocumentacaoBenfeitoria.outroDocumento.descricao ng-disabled=!imovelCtrl.arquivosDocumentacaoBenfeitoria.outroDocumento.descricao flex></div><div layout=row layout-align=\"end center\"><md-button class=\"md-primary primeira-maiuscula\" ng-click=imovelCtrl.adicionarOutroDocumento()>Adicionar</md-button></div><div layout=row ng-show=\"imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.outrosDocumentos.length > 0\"><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column>Descrição</th><th md-column>Arquivo</th><th md-column></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in imovelCtrl.benfeitoria.edificacao.documentacaoEdificacao.outrosDocumentos | limitTo: imovelCtrl.tabelaOutrosDocumentos.limit: (imovelCtrl.tabelaOutrosDocumentos.page - 1) * imovelCtrl.tabelaOutrosDocumentos.limit\"><td md-cell>{{documento.descricao}}</td><td md-cell>{{documento.nomeOriginal}}</td><td md-cell><ng-md-icon icon=delete style=\"fill: #788489\" size=22 alt=\"Excluir Arquivo\" ng-click=imovelCtrl.excluirOutroDocumento(documento)><md-tooltip>Excluir Arquivo</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container></div></v-pane-content></v-pane><!-- Fotos --><v-pane><v-pane-header ng-click=\"vm.validaSecao($event, forms.imagemBenfeitoriaForm, 2, \'.secaoUploadFotosBenfeitoria\')\">Fotos</v-pane-header><v-pane-content><div layout=column><ng-form name=forms.imagemBenfeitoriaForm><div layout=row><md-input-container ng-class=\"forms.imagemBenfeitoriaForm.descricao.$error.required && submittedFoto?\'md-input-invalid\':null\" flex><label>Descrição</label><input ng-model=imovelCtrl.fotoBenfeitoria.descricao type=text name=descricao maxlength=50></md-input-container><div flex-gt-xs class=\"flex-gt-xs margem-top-padrao margem-baixa-padrao\"><label class=matriculas-cs-data-label>Data</label><md-datepicker ng-mask=39/19/9999 restrict=reject class=\"secaoDadosIncorporacao matricula-cs\" ng-model=imovelCtrl.fotoBenfeitoria.dataEmissao md-max-date=imovelCtrl.hoje name=dataEmissao></md-datepicker></div><md-input-container><input type=file ngf-select ng-model=imovelCtrl.fotoFile accept=\".jpeg, .png, .jpg, .bmp\" name=foto aria-label=Arquivo flex></md-input-container></div><div layout=row layout-align=\"end center\"><md-button class=\"md-raised md-primary primeira-maiuscula\" ng-click=imovelCtrl.adicionarFotoBenfeitoria()>Adicionar</md-button></div></ng-form><div layout=column ng-show=\"imovelCtrl.benfeitoria.imagens.length > 0\"><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column></th><th md-column>Descrição</th><th md-column>Data</th><th md-column>Arquivo</th><th md-column></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"imagem in imovelCtrl.benfeitoria.imagens | limitTo: imovelCtrl.tabelaImagensBenfeitoria.limit: (imovelCtrl.tabelaImagensBenfeitoria.page - 1) * imovelCtrl.tabelaImagensBenfeitoria.limit\"><td md-cell><img ng-src=data:image/jpg;base64,{{imagem.imagem}} style=\"width: 70px; height: 40px\"></td><td md-cell>{{imagem.descricao}}</td><td md-cell>{{imagem.dataEmissao | date:\"dd/MM/yyyy\"}}</td><td md-cell>{{imagem.nomeOriginal}}</td><td md-cell><ng-md-icon icon=delete style=\"fill: #788489\" size=22 alt=\"Excluir Foto\" ng-click=\"imovelCtrl.excluirFotoBenfeitoria($event, imagem)\"><md-tooltip>Excluir Arquivo</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=imovelCtrl.tabelaImagensBenfeitoria.limit md-limit-options=imovelCtrl.tabelaImagensBenfeitoria.limitOptions md-page=imovelCtrl.tabelaImagensBenfeitoria.page md-total={{imovelCtrl.benfeitoria.imagens.length}}></md-table-pagination></div></div></v-pane-content></v-pane><!-- Arquivos de Planta --><v-pane><v-pane-header>Arquivos de Planta</v-pane-header><v-pane-content><div layout=column><ng-form name=imovelCtrl.forms.plantaBenfeitoriaForm><div layout=row><md-input-container flex><label>Descrição</label><input ng-model=imovelCtrl.plantaBenfeitoria.nome type=text name=nome maxlength=200></md-input-container><md-input-container flex><label>Data</label><md-datepicker ng-mask=39/19/9999 restrict=reject class=special ng-model=imovelCtrl.plantaBenfeitoria.data md-max-date=imovelCtrl.hoje name=data></md-datepicker></md-input-container><md-input-container><input type=file ngf-select ng-model=imovelCtrl.plantaFile name=planta aria-label=Arquivo flex></md-input-container></div><div layout=row layout-align=\"end center\"><md-button ng-click=imovelCtrl.adicionarPlantaBenfeitoria() md-no-ink class=\"md-raised md-primary primeira-maiuscula\">Adicionar</md-button></div></ng-form><div layout=column ng-show=\"imovelCtrl.benfeitoria.plantas.length > 0\"><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column>Descrição</th><th md-column>Data</th><th md-column>Arquivo (Baixar)</th><th md-column></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"planta in imovelCtrl.benfeitoria.plantas | limitTo: imovelCtrl.tabelaPlantasBenfeitoria.limit: (imovelCtrl.tabelaPlantasBenfeitoria.page - 1) * imovelCtrl.tabelaPlantasBenfeitoria.limit\"><td md-cell>{{planta.nome}}</td><td md-cell>{{planta.data | date:\"dd/MM/yyyy\"}}</td><td md-cell><a download={{planta.nomeOriginal}} href={{planta.url}} target=_blank>{{planta.nomeOriginal}}</a></td><td md-cell><ng-md-icon icon=delete style=\"fill: #788489\" size=22 alt=\"Excluir Planta\" ng-click=\"imovelCtrl.excluirPlantaBenfeitoria($event, planta)\"><md-tooltip>Excluir Arquivo</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=imovelCtrl.tabelaPlantasBenfeitoria.limit md-limit-options=imovelCtrl.tabelaPlantasBenfeitoria.limitOptions md-page=imovelCtrl.tabelaPlantasBenfeitoria.page md-total={{imovelCtrl.benfeitoria.plantas.length}} md-on-paginate=true></md-table-pagination></div></div></v-pane-content></v-pane></div><div layout=row layout-align=\"end center\"><md-button class=\"md-secondary md-raised\" ng-click=imovelCtrl.cancelarInclusaoBenfeitoria();>Fechar</md-button><md-button class=\"md-secondary md-raised\" ng-click=imovelCtrl.limparInclusaoBenfeitoria();>Limpar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=imovelCtrl.salvarBenfeitoria();>Salvar</md-button></div>");
$templateCache.put("scripts-cadastro-imovel/pages/benfeitoria/view/inserir-benfeitoria.tmpl.html","<div layout=row><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=2 imovel=imovel habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout=column flex=70><div layout=column><div campo-ajuda=INC_FORM_BENFEITORIA_PERTEN_PROP ng-class=\"!imovelCtrl.imovel.benfeitoriaPertenceAoProprietario && submittedBenfeitoria ? \'input-invalid\' : null\" layout-margin>Acessão/Benfeitoria pertence ao proprietário? *</div><md-radio-group ng-class=\"!imovelCtrl.imovel.benfeitoriaPertenceAoProprietario && submittedBenfeitoria ? \'radio-invalid\' : null\" ng-model=imovelCtrl.imovel.benfeitoriaPertenceAoProprietario><div layout=row style=\"margin: 0 20px\"><md-radio-button campo-ajuda=INC_FORM_BENFEITORIA_TIPO_SIM value=Sim flex>Sim</md-radio-button><md-radio-button campo-ajuda=INC_FORM_BENFEITORIA_TIPO_NAO value=Não flex>Não</md-radio-button><md-radio-button campo-ajuda=INC_FORM_BENFEITORIA_SEM_INFORM value=\"Sem Informação\" flex>Sem Informação</md-radio-button></div></md-radio-group></div><div layout=column><div layout=row layout-align=\"start start\"><md-button class=\"md-primary md-raised primeira-maiuscula\" ng-click=imovelCtrl.exibirTabInclusaoBenfeitoria();>Adicionar Acessão/Benfeitoria</md-button></div><md-content flex ng-show=\"imovelCtrl.imovel.benfeitorias.length > 0\"><md-table-container flex><table md-table id=tableBenfeitorias><thead md-head><tr md-row><th md-column>Benfeitoria</th><th md-column>Detalhes</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-click=destacaBenfeitoriaMapa(item.codigo); ng-blur=destacaBenfeitoriaMapa(); ng-repeat=\"item in imovelCtrl.imovel.benfeitorias | limitTo: imovelCtrl.paginacaoBenfeitorias.limit: (imovelCtrl.paginacaoBenfeitorias.page - 1) * imovelCtrl.paginacaoBenfeitorias.limit\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-show=item.ativa><td md-cell><img ng-src=\"{{item.imagens[0].imagem != undefined ? \'data:image/jpeg;base64,\'+item.imagens[0].imagem :\'./assets/img/no-image.png\'}}\" style=\"width: 100px; height: 75px\"></td><td md-cell><p><span style=\"font-weight: bold\">ID:</span> {{item.codigo}}</p><p ng-show=\"item.codigo.startsWith(\'E\')\"><span style=\"font-weight: bold\">Tipo de Acessão/Benfeitoria:</span> {{item.edificacao.tipoEspecializacaoEdificacao.descricao}}</p><p ng-show=\"item.codigo.startsWith(\'C\')\"><span style=\"font-weight: bold\">Tipo de Acessão/Benfeitoria:</span> {{item.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao}}</p><p ng-show=\"item.tipoBenfeitoria.id === 1\"><span style=\"font-weight: bold\">Nome:</span> {{item.nome}}</p><p><span style=\"font-weight: bold\">Área Construída(m²):</span> {{item.areaConstruida}}</p></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"imovelCtrl.confirmarExclusaoBenfeitoria(item, $event)\" class=\"md-fab md-raised md-mini\" aria-label=\"Excluir Acessão/Benfeitoria\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir Acessão/Benfeitoria</md-tooltip></md-icon></md-button><md-button ng-click=imovelCtrl.exibirTabInclusaoBenfeitoria(item) class=\"md-fab md-raised md-mini\" aria-label=\"Editar Acessão/Benfeitoria\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Acessão/Benfeitoria</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=imovelCtrl.paginacaoBenfeitorias.limit md-limit-options=imovelCtrl.paginacaoBenfeitorias.limitOptions md-page=imovelCtrl.paginacaoBenfeitorias.page md-total=\"{{ imovelCtrl.imovel.benfeitorias.length }}\" md-page-select ng-disabled=\"imovelCtrl.imovel.benfeitorias.length < 6\"></md-table-pagination></md-content></div><div layout=row layout-xs=column><md-content flex><div layout=row layout-align=\"end center\"><md-button md-no-ink class=\"md-secondary md-raised\" ng-click=imovelCtrl.tabs.previousTab()>Retornar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=imovelCtrl.submitFormularioAcessaoBenfeitoria()>Avançar</md-button></div></md-content></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/home/view/erro-permissao.tmpl.html","<div layout=column ng-cloak flex=100><md-content><div layout=row class=msg-erro-permissao layout-xs=row layout-align=\"center center\"><p>Ops!!! Você não tem acesso a essa funcionalidade!</p></div><div layout=row class=icone-erro layout-xs=row layout-align=\"center center\"><img src=assets/img/negado.jpg></div></md-content></div>");
$templateCache.put("scripts-cadastro-imovel/pages/home/view/home.tmpl.html","<div layout=column ng-cloak flex=100 class=home><md-content><div layout=row layout-xs=column><div layout=column flex=35 class=\"home-box msg\"><header class=home-box-header><h2>Mensagens</h2><p class=indicador-contagem>{{homeCtrl.contadorMensagens}}</p></header><section><md-content><p class=\"mensagem hover\" ng-repeat=\"item in homeCtrl.mensagens | limitTo : 6 \" ng-model-options=\"{trackBy: \'$value.codigo\'}\" ng-click=homeCtrl.mensagemLida($index)><a class=link-mensagem href=#>{{item.data | date : \'dd/MM/yyyy\'}} - {{item.assunto}}</a></p><!--slick infinite=false slides-to-show=1 slides-to-scroll=1 dots=\"true\" arrows=\"true\">\n							<div ng-repeat=\"i in homeCtrl.mensagens track by $index\">\n							</div>\n						</slick--></md-content></section><nav class=carousel><input id=carousel-item-1 type=radio name=carousel-dots><label for=carousel-item-1>Go to item 1</label><input id=carousel-item-2 type=radio name=carousel-dots checked><label for=carousel-item-2>Go to item 2</label><input id=carousel-item-3 type=radio name=carousel-dots><label for=carousel-item-3>Go to item 3</label><input id=carousel-item-4 type=radio name=carousel-dots><label for=carousel-item-4>Go to item 4</label><input id=carousel-item-5 type=radio name=carousel-dots><label for=carousel-item-5>Go to item 5</label><input id=carousel-item-6 type=radio name=carousel-dots><label for=carousel-item-6>Go to item 6</label></nav></div><div layout=column flex=65 class=home-box layout-xs=column><header class=home-box-header><h2>Lista de pendências</h2><p class=indicador-contagem>100</p></header><section><md-content><div layout=row class=pendencia-row><div class=pendencia-item><p class=pendencia-item-contador>31</p><ng-md-icon icon=dashboard style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Cadastro imóveis</p></div><div class=pendencia-item><p class=pendencia-item-contador>32</p><ng-md-icon icon=dashboard style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Imóvel sem habite-sel</p></div><div class=pendencia-item><p class=pendencia-item-contador>33</p><ng-md-icon icon=dns style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Módulo 1</p></div><div class=pendencia-item><p class=pendencia-item-contador>22</p><ng-md-icon icon=home style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Módulo 2</p></div><div class=pendencia-item><ng-md-icon icon=dashboard style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Módulo 3</p></div><div class=pendencia-item><ng-md-icon icon=dashboard style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Módulo 4</p></div></div><div layout=row class=pendencia-row><div class=pendencia-item><p class=pendencia-item-contador>31</p><ng-md-icon icon=polymer style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Módulo 5</p></div><div class=pendencia-item><p class=pendencia-item-contador>12</p><ng-md-icon icon=fingerprint style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Módulo 6</p></div><div class=pendencia-item><ng-md-icon icon=dashboard style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Módulo 7</p></div><div class=pendencia-item><ng-md-icon icon=dashboard style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Módulo 8</p></div><div class=pendencia-item><ng-md-icon icon=dashboard style=fill:#788489 size=48 alt=\"\" ng-click=imovelCtrl.editarBenfeitoria(item)></ng-md-icon><p>Módulo 9</p></div></div></md-content></section></div></div><div layout=row layout-xs=column style=\"margin-top: 20px\"><div layout=column flex=45 style=\"margin-right: 20px\"><div class=\"home-box infos\" style=\"width: 100% !important\"><header class=home-box-header><h2 class=informe-header>Informes</h2></header><md-content><div class=informe-item ng-repeat=\"item in homeCtrl.informes | limitTo : 2\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><p class=informe-data>{{item.data | date : \'dd/MM/yyyy\'}}</p><h2 class=informe-titulo>{{item.titulo}}</h2><p class=informe-descricao>{{homeCtrl.descricaoExtensa(item.descricao)}}<md-button class=btn-continue-lendo ng-click=\"homeCtrl.continuarLendo($index); homeCtrl.showInformesDialog($event)\">continue lendo</md-button></p></div></md-content></div><div><md-button style=\"float: right;color: #4CAF50\" ng-click=\"homeCtrl.resetInformeSelecionado(); homeCtrl.showInformesDialog($event)\"><span>+ Informes</span><md-icon class=icon-forward md-svg-src=assets/img/icons/arrow-point-to-right.svg></md-icon></md-button></div><div class=\"home-box suporte\" style=\"margin-top: 20px;    margin-right: 0px\"><header class=home-box-header><h2 class=informe-header>Suporte</h2></header><md-content><div style=\"float: left; margin-left: 30px\"><img src=assets/img/suporte.png></div><div style=\"float: left; margin-left: 15px\"><p style=\"margin-left: 10px\">Telefone: (99) 9999-9999</p><md-button class=\"md-raised md-primary\">Envie sua dúvida</md-button></div></md-content></div></div><div layout=column flex=55 class=\"home-box pesquisa\" layout-xs=column><header class=home-box-header><h2>Pesquisa de imóvel</h2></header><section><md-content><div ng-include=\"\'scripts-cadastro-imovel/pages/mapa/view/mapa-pesquisa.html\'\"></div></md-content></section></div></div></md-content></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/view/homologar-imovel-detalhar-novo.tmpl.html","<div layout=column><h1>{{ homCtrl.tituloPaginaNOVO }} Homologação do Imóvel</h1><div layout=row><div layout=column><span><label class=fonteNegrito>Número do Processo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.numeroProcesso }}</span><br></span><span><label class=fonteNegrito>RIP:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.rip }}</span><br></span></div><div layout=column style=\"margin-left: 100px\"><span><label class=fonteNegrito>Homologador:</label><span class=span-original-component>{{ usuarioLogado.nome }}</span><br></span><span><label class=fonteNegrito>Cadastrador:</label><span class=span-original-component>{{usuarioLogado.nome}} - {{usuarioLogado.cpf}}</span><br></span></div></div><p></p><md-divider></md-divider><ng-form name=homCtrl.homologarImovelForm><!--    <inc-mensagem no-icon estilo=\"margin: 25px 10px;\">--><!--      <div layout=\"row\">--><!--        <div layout=\"column\" flex=\"50\">--><!--          <div layout=\"row\">--><!--            <p><span style=\"font-weight: bold;\">Número do Processo:</span>--><!--              {{ homCtrl.imovelOriginal.numeroProcesso || \"-\" }}</p>--><!--          </div>--><!--          <div layout=\"row\">--><!--            <p><span style=\"font-weight: bold;\">RIP:</span> {{ homCtrl.imovelOriginal.rip || \"-\" }}</p>--><!--          </div>--><!--        </div>--><!--        <div layout=\"column\" flex=\"50\">--><!--          <div layout=\"row\">--><!--            <p><span style=\"font-weight: bold;\">Homologador:</span> {{ usuarioLogado.nome }}</p>--><!--          </div>--><!--          <div layout=\"row\">--><!--            <p><span style=\"font-weight: bold;\">Cadastrador:</span> {{ nomeCadastrador }}</p>--><!--          </div>--><!--        </div>--><!--      </div>--><!--    </inc-mensagem>--><v-accordion multiple class=vAccordion--default id=detalhamentoImovel><v-pane expanded=homCtrl.exibeDadosLocalizacao><v-pane-header id=dadosDoImovel>Dados de Localização do Imóvel</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/detalhar-imovel-dados-localizacao-novo.tmpl.html\'\"></div></v-pane-content></v-pane><v-pane><v-pane-header id=dadosGeraisImovel>Dados Gerais do Imóvel</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-dados-gerais-imovel/detalhar-imovel-dados-gerais-novo.tmpl.html\'\"></div></v-pane-content></v-pane><v-pane expanded=homCtrl.exibeDadosTerreno><v-pane-header id=dadosDoTerreno>Dados do Terreno</v-pane-header><v-pane-content><div class=titulo-vpane>Geometria do Terreno</div><a href=\"\" ng-click=homCtrl.goToDadosLocalizacao();>VER MAPA DA GEOMETRIA</a><br><br><md-divider></md-divider><br><br><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-terreno/detalhar-imovel-dados-terreno-novo.tmpl.html\'\"></div></v-pane-content></v-pane><v-pane ng-show=\"homCtrl.benfeitorias.length !== 0\"><v-pane-header>Dados da Benfeitorias</v-pane-header><v-pane-content><div class=titulo-vpane>Geometria das Edificações</div><a href=\"\" ng-click=homCtrl.goToDadosLocalizacao();>VER MAPA DA GEOMETRIA</a><br><br><md-divider></md-divider><br><br><span ng-repeat=\"item in homCtrl.benfeitorias\"><div class=titulo-vpane-no-bottom>Dados da Benfeitoria {{ item.benfeitoriaOriginal ? item.benfeitoriaOriginal.nome : item.benfeitoriaAlterada.nome }} - {{ item.benfeitoriaOriginal ? item.benfeitoriaOriginal.tipoBenfeitoria.descricao : item.benfeitoriaAlterada.tipoBenfeitoria.descricao }} / Especialização</div><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/detalhar-imovel-dados-benfeitoria-novo.tmpl.html\'\"></div></span></v-pane-content></v-pane><v-pane ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma !== null || homCtrl.imovelAlterado.unidadeAutonoma !== null || homCtrl.exibeDadosUnidadeAutonoma\"><v-pane-header>Dados da Unidade Autônoma</v-pane-header><v-pane-content><div class=titulo-vpane>Geometria das Edificações</div><a href=\"\" ng-click=homCtrl.goToDadosLocalizacao();>VER MAPA DA GEOMETRIA</a><br><br><md-divider></md-divider><br><br><div class=titulo-vpane-no-bottom>Dados da Edificação</div><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-unidade-autonoma/detalhar-imovel-dados-edificacao-novo.tmpl.html\'\"></div><div class=titulo-vpane-no-bottom>Unidade Autônoma</div><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-unidade-autonoma/detalhar-imovel-dados-unidade-autonoma-novo.tmpl.html\'\"></div></v-pane-content></v-pane><v-pane><v-pane-header>Proprietário</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-dados-gerais-imovel/dados-do-proprietario-novo.tmpl.html\'\"></div></v-pane-content></v-pane></v-accordion><md-input-container class=md-block><md-checkbox ng-model=homCtrl.informacoesRecusada.checkAll ng-change=homCtrl.toggleAll() ng-disabled=homCtrl.isDetalhamento>Recusar todos os dados</md-checkbox></md-input-container><md-input-container class=md-block><label>Observação</label><textarea ng-model=homCtrl.imovelRecusado.observacao ng-required=\"homCtrl.imovelRecusado.camposRecusados.length > 0\" columns=1 md-maxlength=500 rows=2 ng-disabled=homCtrl.isDetalhamento></textarea></md-input-container><div layout=row layout-align=\"end center\"><md-button class=\"md-raised md-primary\" ng-click=homCtrl.fechar()>Fechar</md-button><md-button ng-if=!homCtrl.isDetalhamento exibir-elemento-por-permissao=CAD_IMOVEL.HOMOLOGAR_IMOVEL ng-click=homCtrl.recusarImovel() class=\"md-raised md-primary\">Recusar</md-button><md-button ng-if=!homCtrl.isDetalhamento exibir-elemento-por-permissao=CAD_IMOVEL.HOMOLOGAR_IMOVEL class=\"md-raised md-primary\" ng-disabled=homCtrl.disableHomologacao() ng-click=homCtrl.showHomologarImovel($index)>Homologar</md-button></div></ng-form></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/view/homologar-imovel-detalhar.tmpl.html","<div layout=column><h1>{{ homCtrl.tituloPagina }}</h1><md-divider></md-divider><ng-form name=homCtrl.homologarImovelForm><inc-mensagem tipo=alerta no-icon estilo=\"margin: 25px 10px;\"><div layout=row><div layout=column flex=50><div layout=row><p><span style=\"font-weight: bold\">Número do Processo:</span> {{ homCtrl.imovelOriginal.numeroProcesso || \"-\" }}</p></div><div layout=row><p><span style=\"font-weight: bold\">RIP:</span> {{ homCtrl.imovelOriginal.rip || \"-\" }}</p></div></div><div layout=column flex=50><div layout=row><p><span style=\"font-weight: bold\">Homologador:</span> {{ usuarioLogado.nome }}</p></div><div layout=row><p><span style=\"font-weight: bold\">Cadastrador:</span> {{ nomeCadastrador }}</p></div></div></div></inc-mensagem><v-accordion multiple class=vAccordion--default id=detalhamentoImovel><v-pane expanded=true><v-pane-header id=dadosDoImovel>Dados de Localização do Imóvel</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/detalhar-imovel-dados-localizacao.tmpl.html\'\"></div></v-pane-content></v-pane><v-pane><v-pane-header id=dadosGeraisImovel>Dados Gerais do Imóvel</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-dados-gerais-imovel/detalhar-imovel-dados-gerais.tmpl.html\'\"></div></v-pane-content></v-pane><v-pane><v-pane-header id=dadosDoTerreno>Dados do Terreno</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-terreno/detalhar-imovel-dados-terreno.tmpl.html\'\"></div></v-pane-content></v-pane><v-pane ng-repeat=\"item in homCtrl.benfeitorias\"><v-pane-header>Dados da Benfeitoria - {{ item.benfeitoriaOriginal ? item.benfeitoriaOriginal.codigo : item.benfeitoriaAlterada.codigo }}</v-pane-header><v-pane-content><inc-homologar-dados-benfeitoria benfeitorias=item fn-is-cadastro-alterado=homCtrl.isCadastroAlterado() fn-desabilitar-checkbox=\"homCtrl.desabilitarCheckbox(dadoOriginal, dadoAlterado)\" fn-is-dado-divergente=\"homCtrl.isDadoDivergente(dadoOriginal, dadoAlterado)\" exists=homCtrl.exists(campo) toggle=homCtrl.toggle(element)></inc-homologar-dados-benfeitoria></v-pane-content></v-pane><v-pane ng-if=\"homCtrl.imovelOriginal.tipoImovel.tipoPai === \'UNIDADE AUTÔNOMA\'\"><v-pane-header>Dados da Edificação</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-unidade-autonoma/dados-da-edificacao.tmpl.html\'\"></div></v-pane-content></v-pane><v-pane ng-if=\"homCtrl.imovelOriginal.tipoImovel.tipoPai === \'UNIDADE AUTÔNOMA\'\"><v-pane-header id=dadosDaUnidadeAutonoma>Dados da Unidade Autônoma - {{ homCtrl.imovelOriginal.tipoImovel.descricao }}</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-unidade-autonoma/detalhar-imovel-dados-unidade-autonoma.tmpl.html\'\"></div></v-pane-content></v-pane><v-pane><v-pane-header>Proprietário</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-dados-gerais-imovel/dados-do-proprietario.tmpl.html\'\"></div></v-pane-content></v-pane></v-accordion><md-input-container class=md-block><md-checkbox md-indeterminate=homCtrl.isIndeterminate() ng-checked=homCtrl.isAllChecked() ng-click=homCtrl.toggleAll() ng-disabled=homCtrl.isDetalhamento>Recusar todos os dados</md-checkbox></md-input-container><md-input-container class=md-block><label>Observação</label><textarea ng-model=homCtrl.imovelRecusado.observacao ng-required=\"homCtrl.imovelRecusado.camposRecusados.length > 0\" columns=1 md-maxlength=500 rows=2 ng-disabled=homCtrl.isDetalhamento></textarea></md-input-container><div layout=row layout-align=\"end center\"><md-button class=\"md-raised md-secondary\" ng-click=homCtrl.fechar()>Fechar</md-button><md-button ng-if=!homCtrl.isDetalhamento exibir-elemento-por-permissao=CAD_IMOVEL.HOMOLOGAR_IMOVEL ng-click=homCtrl.recusarImovel() class=\"md-raised md-primary\">Recusar</md-button><md-button ng-if=!homCtrl.isDetalhamento exibir-elemento-por-permissao=CAD_IMOVEL.HOMOLOGAR_IMOVEL class=\"md-raised md-primary\" ng-disabled=homCtrl.disableHomologacao() ng-click=homCtrl.showHomologarImovel($index)>Homologar</md-button></div></ng-form></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/view/homologar-imovel-listar.tmpl.html","<div layout=column layout-align=\"start center\" ng-show=\"consultarHomologarCtrl.resultadoPesquisa.length > 0\"><div class=tabela-pesquisa-imovel><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column>Ação Executada</th><th md-column>Responsável pela Ação</th><th md-column>Imóvel</th><th md-column>Detalhes</th><th md-column>Ação</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in consultarHomologarCtrl.resultadoPesquisa\"><td md-cell style=\"text-align: center\"><p>{{item.dsAcaoExecutada}}</p></td><td md-cell style=\"text-align: center\"><p>{{item.dsNomeUsuarioAcaoExecutada}}</p></td><td md-cell style=\"text-align: center; height: 170px\"><img ng-src=\"{{item.imagens[0].imagem.arquivo != undefined ? \'data:image/jpeg;base64,\'+item.imagens[0].imagem.arquivo :\'./assets/img/no-image.png\'}}\"></td><td md-cell><p ng-show=item.ripValido><span style=\"font-weight: bold\">RIP:</span> {{item.rip}}</p><p ng-show=!item.ripValido><span style=\"font-weight: bold\">RIP:</span> -</p><p ng-show=\"item.enderecamento.pais.descricao == \'Brasil\'\"><span style=\"font-weight: bold\">Endereço:</span> {{item.enderecamento.logradouro}}, Número {{item.numero}}, {{item.enderecamento.bairro}}, {{item.enderecamento.municipio}}/{{item.enderecamento.uf}}-{{item.enderecamento.pais.descricao}}. CEP: {{item.enderecamento.cep}}</p><p ng-show=\"item.enderecamento.pais.descricao != \'Brasil\'\"><span style=\"font-weight: bold\">Endereço:</span> {{item.enderecamento.endereco}}, {{item.complemento}}, {{item.enderecamento.cidadeExterior}} - {{item.enderecamento.pais.descricao}}. Código Postal: {{item.enderecamento.codigoPostal}}.</p><p><span style=\"font-weight: bold\">Situação:</span> {{item.situacaoImovel.descricao}}</p><p><span style=\"font-weight: bold\">Data de cadastramento:</span> {{item.dataCadastro | date : \'dd/MM/yyyy\'}}</p><p><span style=\"font-weight: bold\">Proprietário/Titular:</span> {{item.proprietario.tipoProprietarioReal.descricao}}</p><p ng-show=\"item.incorporacao.numeroMatricula != undefined\"><span style=\"font-weight: bold\">Matrícula/Trascrição:</span> {{item.incorporacao.numeroMatricula}}</p><p ng-show=\"item.incorporacao.numeroMatricula == undefined\"><span style=\"font-weight: bold\">Matrícula/Trascrição:</span> Não informado</p></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=consultarHomologarCtrl.verLocalizacao($event,item) class=\"md-fab md-raised md-mini\" aria-label=\"Ver Localização\"><md-icon ng-bind=\"\'map\'\"><md-tooltip>Ver Localização</md-tooltip></md-icon></md-button><md-button ng-click=consultarHomologarCtrl.irParaHomologacao(item.id) class=\"md-fab md-raised md-mini\" aria-label=\"Detalhar Imóvel para Homologação\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Detalhar Imóvel para Homologação</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=consultarHomologarCtrl.paginacao.limit md-limit-options=consultarHomologarCtrl.paginacao.limitOptions md-page=consultarHomologarCtrl.paginacao.page md-total={{consultarHomologarCtrl.paginacao.totalElements}} md-on-paginate=consultarHomologarCtrl.page md-page-select ng-disabled=\"consultarHomologarCtrl.paginacao.totalElements < 6\"></md-table-pagination><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" ng-click=consultarHomologarCtrl.showLocalizacaoImovelDialog($event,consultarHomologarCtrl.resultadoPesquisa)>Visualizar no Mapa</md-button></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/view/homologar-imovel.tmpl.html","<style>/* Styles go here */\n    .md-chips {\n        width: 100%;\n        white-space: nowrap;\n        overflow-x: scroll;\n    }\n\n    /* Remove  .md-chips .md-chip-input-container\n      if you want the placehoder in a new line*/\n    .md-chips md-chip, .md-chips .md-chip-input-container {\n        display: inline-block !important;\n        float: none !important;\n    }</style><div layout=column><h1>Consulta de Imóveis para Homologação</h1><md-divider></md-divider><ng-form name=consultarHomologarImovelForm ng-keydown=\"$event.keyCode === 13 && consultarHomologarCtrl.validarFormulario()\"><div class=\"tamanho-container primeiro-container margem-horizontal md-block\" layout=row><md-input-container class=\"md-block margem-direita-padrao\" flex=23><label>Número do RIP</label><input campo-ajuda=INC_FORM_CONSULTA_IMOVEL_N_RIP ng-model=consultarHomologarCtrl.imovelPesquisa.rip ng-blur=consultarHomologarCtrl.tratamentoRip()></md-input-container><label style=\"margin-top: 33px\">Período de Cadastramento</label><md-input-container class=md-block flex=18 style=\"margin-right: inherit\"><md-datepicker ng-mask=39/19/9999 restrict=reject name=periodoCadastramentoInicio campo-ajuda=INC_FORM_CONSULTA_IMOVEL_DT_INICIO ng-model=consultarHomologarCtrl.imovelPesquisa.periodoCadastramentoInicio md-max-date=hoje></md-datepicker></md-input-container><md-input-container class=md-block flex=18 style=\"margin-right: 45px\"><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_CONSULTA_IMOVEL_DT_FIM ng-model=consultarHomologarCtrl.imovelPesquisa.periodoCadastramentoFim md-max-date=hoje></md-datepicker></md-input-container><md-input-container class=\"md-block margem-direita-padrao\" flex=18><label>Matrícula do imóvel</label><input ng-model=consultarHomologarCtrl.imovelPesquisa.matriculaImovel></md-input-container><md-input-container class=\"md-block margem-direita-padrao\" flex=18><label>Número do Processo</label><input ng-model=consultarHomologarCtrl.imovelPesquisa.numeroProcesso></md-input-container></div><div class=\"tamanho-container margem-horizontal md-block\" layout=row style=\"height: 90px\"><md-input-container class=\"md-block margem-baixa-padrao\" flex=25><label>Proprietário titular</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_PROP_TIT multiple ng-model=consultarHomologarCtrl.imovelPesquisa.idProprietario><md-option campo-ajuda={{proprietario.codigoSistemico}} ng-repeat=\"proprietario in consultarHomologarCtrl.tiposTitular| orderByBr:\'descricao\'\" value={{proprietario.id}}>{{proprietario .descricao}}</md-option></md-select></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex=25 ng-show=\"consultarHomologarCtrl.imovelPesquisa.idProprietario.includes(\'5\')\n                              || consultarHomologarCtrl.imovelPesquisa.idProprietario.includes(\'6\')\n                              || consultarHomologarCtrl.imovelPesquisa.idProprietario.includes(\'1\')\"><label>CPF/CNPJ do Proprietário</label><md-chips ng-model=consultarHomologarCtrl.imovelPesquisa.cpfCnpjProprietarios name=cpfCnpjProprietarios placeholder=\"um ou mais CPF/CNPJ\" input-aria-label=\"CPF/CNPJ do Proprietário\" readonly md-removable=true md-enable-chip-edit=true md-add-on-blur=true md-transform-chip=consultarHomologarCtrl.formataCpfCnpj($chip)><md-chip-template><strong>{{$chip}}</strong></md-chip-template></md-chips></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex=25><label>Ação Executada</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_PROP_TIT multiple ng-model=consultarHomologarCtrl.imovelPesquisa.acaoExecutada><md-option ng-repeat=\"acao in consultarHomologarCtrl.acoesExecutadas | orderByBr:\'dsAcaoExecutada\'\" value={{acao.cdAcaoExecutada}}>{{acao.dsAcaoExecutada}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-sm flex=24><md-checkbox ng-model=consultarHomologarCtrl.imovelPesquisa.exibirImoveisCadastradosEditadosDoUsuario>Apenas imóveis que eu Analisei</md-checkbox></md-input-container></div><div class=\"tamanho-container margem-horizontal md-block\" layout=row><md-input-container class=md-block flex><label>País</label><md-select campo-ajuda=INC_FORM_HOM_IMOVEL_PAIS ng-model=consultarHomologarCtrl.imovelPesquisa.pais ng-model-options=\"{trackBy: \'$value\'}\" ng-change=consultarHomologarCtrl.buscaCidadesPorPais() required><md-option ng-repeat=\"item in consultarHomologarCtrl.paises | orderByBr:\'descricao\'\" ng-value=item.id ng-selected=\"item.id === 1\">{{ item.descricao }}</md-option></md-select></md-input-container><md-input-container class=md-block ng-show=\"consultarHomologarCtrl.imovelPesquisa.pais === 1\" flex><label>CEP</label><input campo-ajuda=INC_FORM_HOM_IMOVEL_CEP ng-model=consultarHomologarCtrl.imovelPesquisa.cep ui-br-cep-mask ng-blur=consultarHomologarCtrl.buscaEnderecoPorCep()></md-input-container><md-input-container class=md-block ng-show=\"consultarHomologarCtrl.imovelPesquisa.pais !== 1\" flex><md-select campo-ajuda=INC_FORM_HOM_IMOVEL_CID_EXTER name=selectSituacao ng-model=consultarHomologarCtrl.imovelPesquisa.cidadeExterior placeholder=\"Selecione Cidade Exterior\"><md-option ng-repeat=\"cidade in consultarHomologarCtrl.cidades\" value={{cidade}}>{{cidade}}</md-option></md-select></md-input-container><!--    </div>--><!--    <div layout=\"row\">--><md-input-container class=md-block ng-show=\"consultarHomologarCtrl.imovelPesquisa.pais === 1\" flex><label id=labelUf>UF</label><!---\n                <md-select ng-disabled=\"consultarHomologarCtrl.cepPreenchido && consultarHomologarCtrl.cepValido\" ng-model=\"consultarHomologarCtrl.imovelPesquisa.uf\" name=\"uf\" ng-required=\"consultarHomologarCtrl.imovelPesquisa.pais === 1\">\n                    <md-option ng-repeat=\"uf in consultarHomologarCtrl.ufs\" ng-value=\"uf.sigla\" ng-model-options=\"{trackBy: \'$value.id\'}\">\n                        {{ uf.descricao }}\n                    </md-option>\n                </md-select>\n            --><md-autocomplete ng-disabled=\"consultarHomologarCtrl.cepPreenchido && consultarHomologarCtrl.cepValido\" md-selected-item=consultarHomologarCtrl.imovelPesquisa.uf name=uf ng-required=\"consultarHomologarCtrl.imovelPesquisa.pais === 1 && !consultarHomologarCtrl.imovelPesquisa.rip\" md-clear-button=consultarHomologarCtrl.apresentaBotao() md-selected-item-change=consultarHomologarCtrl.pesquisaMunicipio(consultarHomologarCtrl.imovelPesquisa.uf) md-search-text=$scope.searchUf md-no-cache md-items=\"item in consultarHomologarCtrl.ufs | filter:{sigla:$scope.searchUf} | orderByBr:\'sigla\'\" md-item-text=item.sigla md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=labelUf><md-item-template><span md-highlight-text=$scope.searchUf md-highlight-flags=^i>{{item.sigla}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchUf}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container clas=md-block ng-show=\"consultarHomologarCtrl.imovelPesquisa.pais === 1\" flex><label id=labelMunicipio>Município</label><!--\n                <input campo-ajuda=\"INC_FORM_HOM_IMOVEL_MUNICIPIO\"\n                ng-disabled=\"!consultarHomologarCtrl.isCepGenerico && consultarHomologarCtrl.cepValido\"\n                ng-model=\"consultarHomologarCtrl.imovelPesquisa.municipio\">\n                --><md-autocomplete campo-ajuda=INC_FORM_HOM_IMOVEL_MUNICIPIO name=municipio md-no-cache=true md-selected-item-change=consultarHomologarCtrl.selecionaMunicipio() ng-disabled=\"!consultarHomologarCtrl.isCepGenerico && consultarHomologarCtrl.cepValido\" md-clear-button=consultarHomologarCtrl.apresentaBotao() md-selected-item=consultarHomologarCtrl.imovelPesquisa.municipio md-search-text=$scope.searchMunicipio md-items=\"item in consultarHomologarCtrl.municipios | filter:{nome:$scope.searchMunicipio} | orderByBr:\'nome\'\" md-item-text=item.nome md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=labelMunicipio><md-item-template><span md-highlight-text=$scope.searchMunicipio md-highlight-flags=^i>{{item.nome}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchMunicipio}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container></div><div layout=row layout-align=\"end center\"><md-button type=reset class=\"md-primary md-raised\" ng-click=consultarHomologarCtrl.limpar()>Limpar</md-button><md-button class=\"md-primary md-raised\" ng-click=consultarHomologarCtrl.validarFormulario()>Pesquisar</md-button></div></ng-form><div flex ng-include=\"\'scripts-cadastro-imovel/pages/homologar/view/homologar-imovel-listar.tmpl.html\'\"></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/abas.tmpl.html","<h1>Detalhamento do Imóvel / RIP: {{ vm.imovel.rip || \"-\" }} - {{vm.imovel.situacaoImovel.descricao}} <span ng-if=vm.imovel.isSuspenso>- SUSPENSO {{vm.getRestricoes()}}</span></h1><h2 ng-if=vm.imovel.cancelado style=\"color: gray; font-size: small\">Cancelado por: {{vm.motivoCancelamento}}</h2><h2 ng-if=\"vm.imovel.dsSpiunet && vm.imovel.dsSpiunet !== \'\' && vm.imovel.dsSpiunet !== \'pend. sincronizacao\'\" style=\"color: green; font-size: small\">RIP SPIU: {{vm.imovel.dsSpiunet || \"-\"}}</h2><div class=\"inc-mensagem-container inc-mensagem-erro\" layout=row ng-show=\"!vm.imovel.cancelado && (vm.temPendenciaMigracao() || vm.temPendenciaSincronizacao())\"><md-icon style=\"margin-top: 15px\">report</md-icon><div><!--Pendencia de sincronização--><p class=inc-mensagem-titulo ng-if=vm.temPendenciaSincronizacao()>Imóvel pendente de sincronização com o SPIUnet</p><p ng-show=\"vm.mostrarPendencia && !!vm.erroSincronizacao\">Erro ao sincronizar: {{vm.erroSincronizacao}}</p><!--Pendencia de migração--><p class=inc-mensagem-titulo ng-if=vm.temPendenciaMigracao()>Imóvel oriundo da migração de imóveis do sistema SPIUnet, pendente de validação.</p><div ng-show=\"vm.mostrarPendencia && vm.temPendenciaMigracao()\"><p>Dicas de avaliação:</p><ul><li>Verifique o número do endereço do imóvel, ele não pode conter letras ou símbolos</li><li>Verifique o tipo de imóvel</li><li>Verifique o CEP do imóvel</li><li>Verifique o georeferenciamento do imóvel</li><li>Verifique os dados cartoriais e processo SEI do imóvel</li></ul><p>Essa lista não é exaustiva, é necessária uma avaliação completa do cadastro.</p></div><button class=md-button ng-click=vm.alterarMostrarPendencia()>Mostrar {{vm.mostrarPendencia ? \'menos\' : \'mais\'}}</button></div></div><md-divider></md-divider><form name=formGeral class=noPadding><div layout=column layout-xs=column><div ng-cloak flex=100 flex-xs=100><md-content style=\"min-height: 500px\"><md-tabs md-border-bottom md-dynamic-height ng-show=\"!vm.isInclusaoBenfeitoria && !vm.mostraTabTituloAquisitivo\"><md-tab ng-repeat=\"tab in vm.tabs.tabs\" label={{tab.nome}} ng-click=selectTab(tab.index) ng-disabled=\"tab.index != vm.tabs.selectedIndex && !vm.isDetail\" md-active=\"vm.tabs.selectedIndex == tab.index\"><div ng-include=tab.detail></div></md-tab></md-tabs><md-tabs md-border-bottom md-dynamic-height ng-if=vm.isInclusaoBenfeitoria><md-tab label={{vm.tabs.tabInclusaoBenfeitoria.nome}}><inclusao-benfeitoria is-detail=true action=detail benfeitoria=vm.benfeitoria imovel=vm.imovel show-inclusao-benfeitoria=vm.isInclusaoBenfeitoria show-tab-form-documento=vm.mostraTabFormDocumento documento=vm.documentoBenfeitoria></inclusao-benfeitoria></md-tab></md-tabs><md-tabs md-border-bottom md-dynamic-height ng-if=vm.mostraTabTituloAquisitivo><md-tab label={{vm.tabs.tabInclusaoTituloAquisitivo.nome}}><titulo-aquisitivo titulo=vm.titulo titulo-backup=vm.tituloBackup imovel=vm.imovel show-titulo-aquisitivo=vm.mostraTabTituloAquisitivo query-pagination=vm.paginationTituloAquisitivo detail=true></titulo-aquisitivo><div ng-include=vm.tabs.tabInclusaoTituloAquisitivo.template></div></md-tab></md-tabs></md-content></div></div></form>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/consultar-imovel.tmpl.html","<style>/* Styles go here */\n  .md-chips {\n    width: 100%;\n    white-space: nowrap;\n    overflow-x: scroll;\n  }\n\n  /* Remove  .md-chips .md-chip-input-container\n      if you want the placehoder in a new line*/\n  .md-chips md-chip,\n  .md-chips .md-chip-input-container {\n    display: inline-block !important;\n    float: none !important;\n  }</style><h1 class=margem-horizontal>Consulta de Imóveis</h1><md-divider class=margem-horizontal></md-divider><ng-form name=formConsultarImovel ng-keydown=\"$event.keyCode === 13 && vm.tratamentoRip() && vm.pesquisar()\"><div class=\"tamanho-container primeiro-container margem-horizontal md-block\" layout=row><md-input-container class=\"md-block margem-direita-padrao\" flex=20><label>Número do RIP</label><input campo-ajuda=INC_FORM_CONSULTA_IMOVEL_N_RIP ng-model=vm.imovel.rip ng-blur=vm.tratamentoRip() placeholder=\"Informe o RIP: SPIUnet, SPUnet...\"></md-input-container><md-input-container class=md-block flex=16><label>Situação do imóvel</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_SIT_IMOVEL multiple ng-model=vm.imovel.idSituacaoImovel><md-option campo-ajuda={{situacao.codigoSistemico}} ng-repeat=\"situacao in vm.situacoes| orderByBr:\'descricao\'\" value={{situacao.id}}>{{situacao.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=18 style=\"margin-right: inherit\"><label>Data Inicial</label><md-datepicker ng-mask=39/19/9999 restrict=reject name=periodoCadastramentoInicio campo-ajuda=INC_FORM_CONSULTA_IMOVEL_DT_INICIO ng-model=vm.imovel.periodoCadastramentoInicio md-max-date=hoje></md-datepicker></md-input-container><md-input-container class=md-block flex=18 style=\"margin-right: 45px\"><label>Data final</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_CONSULTA_IMOVEL_DT_FIM ng-model=vm.imovel.periodoCadastramentoFim md-max-date=hoje></md-datepicker></md-input-container><md-input-container class=\"md-block margem-direita-padrao\" flex=16><label>Matrícula do imóvel</label><input ng-model=vm.matricula></md-input-container><md-input-container class=\"md-block margem-direita-padrao\" flex=16><label>Número do Processo</label><input ng-model=vm.imovel.numeroProcesso></md-input-container></div><div class=\"tamanho-container margem-horizontal md-block\" layout=row style=\"height: 90px\"><md-input-container class=\"md-block margem-baixa-padrao\" flex=25><label>Proprietário titular</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_PROP_TIT ng-model=vm.imovel.idProprietario multiple><md-option campo-ajuda={{proprietario.codigoSistemico}} ng-repeat=\"proprietario in vm.tiposTitular| orderByBr:\'descricao\'\" value=\"{{proprietario .id}}\">{{proprietario .descricao}}</md-option></md-select></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex=25 ng-show=\"vm.imovel.idProprietario.includes(\'5\') || vm.imovel.idProprietario.includes(\'6\') || vm.imovel.idProprietario.includes(\'1\')\"><label>CPF/CNPJ do Proprietário</label><md-chips ng-model=vm.cpfCnpjProprietarios name=cpfCnpjProprietarios placeholder=\"um ou mais CPF/CNPJ\" input-aria-label=\"CPF/CNPJ do Proprietário\" readonly md-removable=true md-enable-chip-edit=true md-add-on-blur=true md-transform-chip=vm.formataCpfCnpj($chip)><md-chip-template><strong>{{$chip}}</strong></md-chip-template></md-chips></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex=25><label>Ação Executada</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_PROP_TIT multiple ng-model=vm.imovel.enderecamento.acaoExecutada><md-option ng-repeat=\"acao in vm.acoesExecutadas | orderByBr:\'dsAcaoExecutada\'\" value={{acao.cdAcaoExecutada}}>{{acao.dsAcaoExecutada}}</md-option></md-select></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex=25><label>Pendências</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_PROP_TIT multiple ng-model=vm.imovel.pendencias><md-option ng-repeat=\"obj in vm.pendencias | orderByBr:\'pendencia\'\" ng-if=\"obj.pendencia !== \'Homologar Cadastro/Edição\'\" value={{obj.pendencia}} ng-selected=\"obj.pendencia === vm.pendenciaSelecionada\">{{obj.pendencia}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-sm flex=24><md-checkbox ng-model=vm.exibirImoveisCadastradosEditadosDoUsuario>Apenas imóveis que eu cadastrei/editei</md-checkbox></md-input-container></div><div class=\"tamanho-container margem-horizontal\" layout=row><md-input-container class=\"md-block margem-baixa-padrao\" flex=25><label>País</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_PAIS ng-change=vm.buscaCidadesPorPais() ng-required=!vm.imovel.rip ng-model=vm.imovel.enderecamento.pais ng-model-options=\"{trackBy: \'$value.id\'}\" name=pais><md-option ng-repeat=\"item in vm.paises| orderByBr:\'descricao\'\" ng-value=item ng-selected=\"item.id === 1\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=25 ng-show=\"vm.imovel.enderecamento.pais.id === 1\"><label>CEP</label><input campo-ajuda=INC_FORM_CONSULTA_IMOVEL_CEP ng-model=vm.imovel.enderecamento.cep ui-br-cep-mask ng-change=vm.buscaEnderecoPorCep()></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex=20 ng-show=\"vm.imovel.enderecamento.pais.id === 1\"><label id=ufLabel>UF</label><!--<md-select\n        campo-ajuda=\"INC_FORM_CONSULTA_IMOVEL_UF\"\n        ng-model=\"vm.imovel.enderecamento.uf\"\n        ng-disabled=\"vm.cepPreenchido && vm.cepValido\"\n        name=\"uf\"\n        ng-required=\"vm.imovel.enderecamento.pais.id === 1 && !vm.imovel.rip\"\n        required\n      >\n        <md-option\n          ng-repeat=\"uf in vm.ufs| orderByBr:\'descricao\'\"\n          value=\"{{uf.sigla}}\"\n          ng-model-options=\"{trackBy : \'$value.id\'}\"\n          ng-selected=\"uf.sigla === vm.imovel.enderecamento.uf\"\n        >\n          {{uf.descricao}}\n        </md-option>\n      </md-select>--><md-autocomplete campo-ajuda=INC_FORM_CONSULTA_IMOVEL_UF ng-required=\"vm.imovel.enderecamento.pais.id === 1 && !vm.imovel.rip\" ng-selected=\"uf.sigla === vm.imovel.enderecamento.uf\" ng-disabled=\"vm.cepPreenchido && vm.cepValido\" md-selected-item-change=vm.pesquisaMunicipio(vm.imovel.enderecamento.uf.sigla) name=uf md-clear-button=vm.apresentaBotao() md-selected-item=vm.imovel.enderecamento.uf md-search-text=$scope.searchUf md-items=\"uf in vm.ufs| filter:{sigla:$scope.searchUf} | orderByBr:\'sigla\'\" md-item-text=uf.sigla md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=ufLabel><md-item-template><span md-highlight-text=$scope.searchUf md-highlight-flags=^i>{{uf.sigla}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchUf}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block flex=20 ng-show=\"vm.imovel.enderecamento.pais.id === 1\"><label id=municipioLabel>Município</label><!--<input\n        campo-ajuda=\"INC_FORM_CONSULTA_IMOVEL_MUNIC\"\n        type=\"text\"\n        ng-model=\"vm.imovel.enderecamento.municipio\"\n        ng-disabled=\"!vm.isCepGenerico && vm.cepValido\"\n      />--><md-autocomplete campo-ajuda=INC_FORM_CONSULTA_IMOVEL_MUNIC ng-disabled=\"!vm.isCepGenerico && vm.cepValido\" name=municipio md-clear-button=vm.apresentaBotao() md-selected-item=vm.imovel.enderecamento.municipio md-search-text=$scope.searchMunicipio md-items=\"municipio in vm.municipios | filter:{nome:$scope.searchMunicipio} | orderByBr:\'nome\'\" md-item-text=municipio.nome md-min-length=0 md-escape-options=clear md-select-on-match md-no-cache md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=municipioLabel><md-item-template><span md-highlight-text=$scope.searchMunicipio md-highlight-flags=^i>{{municipio.nome}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchMunicipio}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block flex=40 ng-hide=\"vm.imovel.enderecamento.pais.id === 1 || vm.imovel.enderecamento.pais.id == null \"><label>Cidade Exterior</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_CID_EXT ng-model=vm.imovel.enderecamento.cidadeExterior>><md-option ng-repeat=\"cidade in vm.cidades\" value={{cidade}}>{{cidade}}</md-option></md-select></md-input-container></div><div class=\"separador margem-horizontal\"></div><div class=margem-horizontal-botoes layout=row layout-align=\"end right\"><md-button type=reset class=\"md-secondary md-raised\" ng-click=limpar()>Limpar</md-button><md-button class=\"md-primary md-raised\" exibir-elemento-por-permissao=CAD_IMOVEL.CADASTRAR_IMOVEL href=/#/incorporacao/inserirCadastro>Novo</md-button><md-button class=\"md-primary md-raised\" ng-click=vm.pesquisar()><md-icon md-svg-src=assets/img/icons/search.svg aria-label=Fechar></md-icon>Pesquisar</md-button></div></ng-form><div layout=row layout-align=\"center start\" ng-show=\"vm.resultadoPesquisa.length > 0\"><div flex=90><md-table-container><table md-table md-progress=promise ng-model=vm.imovelSelecionado><thead md-head><tr md-row><th md-column>Ação Executada</th><th md-column>Responsável pela Ação</th><th md-column>Imóvel</th><th md-column>Detalhes</th><th md-column>Ação</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in vm.resultadoPesquisa\"><td md-cell style=\"text-align: center\"><p>{{item.dsAcaoExecutada}}</p></td><td md-cell style=\"text-align: center\"><p>{{item.nomeResponsavelAcaoExecutada}}</p></td><td md-cell style=\"text-align: center\"><p><img ng-src=\"{{item.imagens[0].imagem.arquivo !=undefined ? \'data:image/jpeg;base64,\'+item.imagens[0].imagem.arquivo :\'./assets/img/no-image.png\'}}\" style=\"max-height: 115px; margin: 3px 0\"></p></td><td md-cell class=shine ng-click=detalharImovel(item.id);><p style=\"padding: 0 15px\"><span style=\"font-weight: bold\">Tipo de imóvel:</span> {{item.tipoImovel.tipoPai === \"UNIDADE AUTÔNOMA\" ? \"Unidade Autônoma / \" + item.tipoImovel.descricao : item.tipoImovel.descricao}}</p><p style=\"padding: 0 15px\"><span><span style=\"font-weight: bold\">RIP:</span> {{item.ripValido ? item.rip : \'-\'}}</span> <span style=\"font-weight: bold; margin-left: 10px\">Situação:</span> {{item.situacaoImovel.descricao}} <span style=\"font-weight: bold; margin-left: 10px\">Data de cadastramento:</span> {{item.dataCadastro | date : \'dd/MM/yyyy\'}}</p><p style=\"padding: 0 15px\" ng-show=\"item.enderecamento.pais.descricao == \'Brasil\'\"><span style=\"font-weight: bold\">Endereço:</span> {{item.enderecamento.tipoLogradouro}} {{item.enderecamento.logradouro}}, Número: {{item.numero}}, {{item.enderecamento.bairro}}, {{item.enderecamento.municipio}}/{{item.enderecamento.uf}}-{{item.enderecamento.pais.descricao}}. CEP: {{item.enderecamento.cep}}</p><p style=\"padding: 0 15px\" ng-show=\"item.enderecamento.pais.descricao != \'Brasil\'\"><span style=\"font-weight: bold\">Endereço:</span> {{item.enderecamento.endereco}}, {{item.complemento}}, {{item.enderecamento.cidadeExterior}} - {{item.enderecamento.pais.descricao}}. Código Postal: {{item.enderecamento.codigoPostal}}.</p><p style=\"padding: 0 15px\"><span style=\"font-weight: bold\">Proprietário/Titular:</span> {{item.proprietario.tipoProprietarioReal.descricao}}</p><p style=\"padding: 0 15px\"><span style=\"font-weight: bold\">Matrículas/Transcrição:</span> {{item.incorporacao.matriculas.length > 0 ? item.matriculas : \'Não informado\'}}</p><p style=\"padding: 0 15px\" ng-if=item.isSuspenso><span style=\"font-weight: bold\">SUSPENSO {{vm.getRestricoes(item)}}</span></p><p ng-show=\" !!item.pendencias && item.pendencias.length > 0 && vm.getTituloPendencia(item) != null\" style=\"padding: 0 15px; color: red\"><span style=\"font-weight: bold\">Descrição da pendência:</span> {{vm.getTituloPendencia(item)}}</p></td><td md-cell><div class=container-acoes><md-fab-speed-dial id=menuConsultarOpcoes class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=vm.gerarFolhaDeRosto(item.id) class=\"md-fab md-raised md-mini\" aria-label=\"Gerar PDF\"><md-icon ng-bind=\"\'picture_as_pdf\'\"><md-tooltip>Gerar PDF</md-tooltip></md-icon></md-button><md-button ng-click=vm.verificaCancelamento(item,$event,$index) class=\"md-fab md-raised md-mini\" exibir-elemento-por-permissao=CAD_IMOVEL.CANCELAR_IMOVEL ng-show=vm.exibeCancelar(item) aria-label=\"Cancelar Imóvel\"><md-icon ng-bind=\"\'close\'\"><md-tooltip>Cancelar Imóvel</md-tooltip></md-icon></md-button><md-button id=botaoEditar ng-click=vm.irParaEdicao(item) class=\"md-fab md-raised md-mini\" exibir-elemento-por-permissao=CAD_IMOVEL.CADASTRAR_IMOVEL ng-show=vm.exibeEdicao(item) aria-label=\"Editar Imóvel\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Imóvel</md-tooltip></md-icon></md-button><md-button ng-click=vm.irParaDetalhamento(item.id) class=\"md-fab md-raised md-mini\" aria-label=\"Detalhar Imóvel\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Detalhar Imóvel</md-tooltip></md-icon></md-button><md-button ng-click=\"vm.showLocalizacaoImovelDialog($event, buscarImovelMapa(item))\" class=\"md-fab md-raised md-mini\" aria-label=\"Ver Localização\"><md-icon ng-bind=\"\'map\'\"><md-tooltip>Ver Localização</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=vm.paginacao.limit md-limit-options=vm.paginacao.limitOptions md-page=vm.paginacao.page md-total={{vm.paginacao.totalElements}} md-on-paginate=vm.pesquisar md-page-select ng-disabled=\"vm.paginacao.totalElements < 5\"></md-table-pagination><div layout=row layout-align=end><!--md-button class=\"md-raised md-primary\" ng-click=\"vm.exportarPesquisa()\">Exportar</md-button--><md-button class=\"md-primary md-raised\" ng-click=\"vm.showLocalizacaoImovelDialog($event, buscarTodosImoveis())\">Visualizar no Mapa</md-button></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/dados-incorporacao.tmpl.html","<div layout-gt-xs=row><md-input-container ng-class=\"forms.imovelForm.numeroProcesso.$error.required && submittedImovel? \'md-input-invalid \': null\" class=md-block flex ng-if=\"imovel.incorporacao.tipoIncorporacao.id === 2\n&& (imovel.incorporacao.tipoAquisicao.id != 9 && imovel.incorporacao.tipoAquisicao.id != null)\"><label>Número do Processo 2</label><input class=secaoDadosIncorporacao ng-model=imovel.numeroProcesso name=numeroProcesso md-maxlength=17 ng-required=\"imovel.incorporacao.tipoIncorporacao.id === 2\n&& (imovel.incorporacao.tipoAquisicao.id != 9 && imovel.incorporacao.tipoAquisicao.id != null)\" maxlength=17></md-input-container></div><div layout-gt-sm=row><md-input-container ng-class=\"forms.imovelForm.tipoIncorporacao.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=md-block flex><label>Forma de Incorporação</label><md-select class=secaoDadosIncorporacao ng-model=imovel.incorporacao.tipoIncorporacao ng-model-options=\"{trackBy: \'$value.id\'}\" required ng-disabled=\"imovelCtrl.valorTipoIncorporcao === 1\" name=tipoIncorporacao><md-option ng-repeat=\"item in imovelCtrl.tiposIncorporacao\" ng-value=item ng-selected=\"item.id === imovelCtrl.valorTipoIncorporcao\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.imovelForm.tipoAquisicao.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=md-block flex ng-show=\"imovel.incorporacao.tipoIncorporacao.id === 2\"><label>Tipo de Aquisição</label><md-select class=secaoDadosIncorporacao ng-model=imovel.incorporacao.tipoAquisicao ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"imovel.incorporacao.tipoIncorporacao.id === 2\" name=tipoAquisicao><md-option ng-repeat=\"item in imovelCtrl.tiposAquisicao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.imovelForm.tipoContrato.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=md-block flex ng-show=\"imovel.incorporacao.tipoIncorporacao.id === 5\"><label>Contrato Pessoal</label><md-select class=secaoDadosIncorporacao ng-model=imovel.incorporacao.tipoContratoPessoal ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoContrato ng-required=\"imovel.incorporacao.tipoIncorporacao.id === 5\"><md-option ng-repeat=\"item in imovelCtrl.tiposContratoPessoal\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.imovelForm.tipoFracionamento.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=md-block flex ng-show=\"imovel.incorporacao.tipoIncorporacao.id === 3\"><label>Tipo de Fracionamento</label><md-select class=secaoDadosIncorporacao ng-model=imovel.tipoFracionamento ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoFracionamento ng-required=\"imovel.incorporacao.tipoIncorporacao.id === 3\"><md-option ng-repeat=\"item in imovelCtrl.tiposFracionamento\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><!-- Título aquisitivo --><div ng-if=\"imovel.incorporacao.tipoIncorporacao.id === 2 && imovel.incorporacao.tipoAquisicao.id != 9\"><fieldset-titulo-aquisitivo select-index=selectedIndex titulos=imovelCtrl.imovel.titulos exibe-tab-titulo=imovelCtrl.mostraTabTituloAquisitivo imovel=imovelCtrl.imovel titulo=imovelCtrl.titulo titulo-backup=imovelCtrl.tituloBackup query-pagination=imovelCtrl.paginationTituloAquisitivo></fieldset-titulo-aquisitivo></div><!-- Remembramento de imóveis --><div layout=column ng-show=\"imovel.incorporacao.tipoIncorporacao.id === 4\"><div layout-gt-xs=row><md-input-container class=md-block flex-xs=100 flex=60><label>RIP do Imóvel Remembrado</label><input type=text class=secaoDadosIncorporacao ng-model=imovel.incorporacao.ripImovelRemembrado ng-change=imovelCtrl.validarRipFusao() maxlength=8></md-input-container><md-button class=\"md-primary md-raised\" style=\"padding: 0px 10px;margin: auto; margin-left: 5px\" ng-click=imovelCtrl.pesquisaImovelPorRip()>Adicionar</md-button></div><!-- Tabela --><div class=mgt-25 layout-gt-xs=row><md-content flex-xs=100 flex=100><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column>RIP</th><th md-column>Endereço do imóvel</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"dadosRip in imovel.ripsFusao | limitTo: tabela.limit: (tabela.page - 1) * tabela.limit\"><td md-cell class=inc-text-center>{{dadosRip.rip}}</td><td md-cell class=inc-text-center>{{dadosRip.enderecamento.tipoLogradouro}} {{dadosRip.enderecamento.logradouro}}, Número {{dadosRip.numero}}, {{dadosRip.enderecamento.bairro}}, {{dadosRip.enderecamento.municipio}}/{{dadosRip.enderecamento.uf}}-{{dadosRip.enderecamento.pais.descricao}}. CEP: {{dadosRip.enderecamento.cep}}</td><td md-cell class=inc-text-center><div layout=row layout-align=\"center stretch\"><a target=_blank ui-sref=\"INCORPORACAO.detalharImovel({id: dadosRip.id})\" alt=\"Detalhar RIP\" style=\"margin-top: 8px\"><ng-md-icon icon=search style=fill:#788489><md-tooltip>Detalhar RIP de Origem</md-tooltip></ng-md-icon></a><md-button class=md-icon-button alt=\"Remover RIP\"><ng-md-icon icon=delete style=fill:#788489 ng-click=imovelCtrl.removeRip(dadosRip)><md-tooltip>Remover RIP</md-tooltip></ng-md-icon></md-button></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabela.limit md-limit-options=tabela.limitsPage md-page=tabela.page md-total={{imovel.ripsFusao.length}} md-on-paginate=true></md-table-pagination></md-content></div></div><!--mudado de Outro Contrato Pessoal para Descrição do Contrato/Termo--><md-input-container ng-class=\"forms.imovelForm.outroContatoPessoal.$error.required && submittedImovel? \'md-input-invalid \': null\" class=md-block flex ng-if=\"imovel.incorporacao.tipoIncorporacao.id === 5 && imovel.incorporacao.tipoContratoPessoal.id === 6\"><label>Descrição do Contrato/Termo</label><input class=secaoDadosIncorporacao ng-model=imovel.incorporacao.outroContatoPessoal ng-required=\"imovel.incorporacao.tipoContratoPessoal.id === 6\" name=outroContatoPessoal></md-input-container><md-input-container ng-if=\"imovel.incorporacao.tipoAquisicao.id === 8\"><md-checkbox class=secaoDadosIncorporacao ng-model=imovel.incorporacao.doacaoComEncargo>Doação com Encargo</md-checkbox></md-input-container><div layout-gt-sm=row flex-gt-xs layout-align=\"center center\" ng-show=\"imovel.incorporacao.tipoIncorporacao.id === 2 && imovel.incorporacao.tipoAquisicao.id === 8 && imovel.incorporacao.doacaoComEncargo === true\"><md-input-container ng-class=\"forms.imovelForm.valorEncargo.$error.required && submittedImovel? \'md-input-invalid \': null\" class=md-block flex=65><label>Encargo Aquisição</label><input class=secaoDadosIncorporacao ng-model=imovel.incorporacao.valorEncargo ng-required=\"imovel.incorporacao.tipoIncorporacao.id === 2 && imovel.incorporacao.tipoAquisicao.id === 8 && imovel.incorporacao.doacaoComEncargo === true\" name=valorEncargo></md-input-container><h5>Data Final*</h5><md-datepicker ng-mask=39/19/9999 restrict=reject ng-class=\"forms.imovelForm.dataFinalEncargo.$error.required && submittedImovel? \'md-input-invalid \': null\" flex class=secaoDadosIncorporacao ng-model=imovel.incorporacao.dataFinalEncargo md-placeholder=\"Data Final\" md-max-date=imovelCtrl.hoje ng-required=\"imovel.incorporacao.tipoIncorporacao.id === 2 && imovel.incorporacao.tipoAquisicao.id === 8 && imovel.incorporacao.doacaoComEncargo === true\" name=dataFinalEncargo></md-datepicker></div><md-input-container ng-class=\"forms.imovelForm.fundamento.$error.required && submittedImovel? \'md-input-invalid\': null\" class=md-block flex ng-show=\"imovel.incorporacao.tipoIncorporacao.id ===1\" ng-required=\"imovel.incorporacao.tipoIncorporacao.id ===1\"><label>Fundamento</label><md-select multiple class=secaoDadosIncorporacao ng-model=imovel.incorporacao.fundamento ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"imovel.incorporacao.tipoIncorporacao.id === 1\" name=fundamento><md-option ng-repeat=\"item in imovelCtrl.fundamentos\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div layout=row ng-show=\"imovel.incorporacao.tipoIncorporacao.id === 3\"><div flex=50><md-input-container class=md-block><label>RIP de Origem</label><input class=secaoDadosIncorporacao ng-model=imovel.incorporacao.codigoOrigem ng-change=imovelCtrl.validarRip() ng-required=\"imovel.incorporacao.tipoIncorporacao.id === 3\" ng-minlength=8 name=ripOrigem></md-input-container></div><div flex=50 style=\"margin-top: 25px\"><div layout=row><nao-sei-rip style=\"margin-left: 15px\" flex=30 text=\"Não sei o RIP\" multiple-selection=false imovel=imovel></nao-sei-rip><ng-md-icon ng-show=idImovelOrigem icon=search style=fill:#788489><md-tooltip>Detalhar RIP de Origem</md-tooltip></ng-md-icon><a flex ng-show=idImovelOrigem target=_blank ui-sref=\"INCORPORACAO.detalharImovel({id: idImovelOrigem})\">Detalhar RIP de Origem</a></div></div></div><md-input-container class=md-block flex ng-show=imovelCtrl.showSituacaoIncorporacao><label>Situação da Incorporação</label><md-select class=secaoDadosIncorporacao ng-model=imovel.incorporacao.situacaoIncorporacao ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"imovelCtrl.valorSituacaoIncorporacao === 3\" name=situacaoIncorporacao ng-required=true><md-option ng-repeat=\"item in imovelCtrl.situacoesIncoporacao\" ng-value=item ng-selected=\"item.id === imovelCtrl.valorSituacaoIncorporacao\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.imovelForm.direitoAdquiridoIncorporado.$error.required && submittedImovel? \'md-input-invalid\': null\" class=md-block flex ng-show=\"imovel.incorporacao.tipoIncorporacao.id === 2\"><label>Direito Consulta de Imóveis com Pendência/Incorporado</label><md-select class=secaoDadosIncorporacao ng-model=imovel.incorporacao.direitoIncorporado ng-model-options=\"{trackBy: \'$value.id\'}\" md-selected=\"valor.id === imovelCtrl.valorDireitoAdquiridoIncorporado\" ng-disabled=\"imovelCtrl.valorDireitoAdquiridoIncorporado === 1 && imovel.incorporacao.tipoIncorporacao\" ng-required=imovelCtrl.direitoAdquiridoIsRequiredAndVisible name=direitoAdquiridoIncorporado><md-option ng-repeat=\"item in imovelCtrl.direitosIncorporados\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><!--  Matrícula/Transcrição --><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/fieldsets/matricula-transcricao-fieldset.html\'\"></div><div layout=column ng-show=\"imovel.incorporacao.dataMatricula && imovel.incorporacao.numeroMatricula\"><div layout=row layout-align=\"left end\" layout-wrap><md-button class=\"md-raised md-primary\" ng-click=\"imovelCtrl.resetarCertidaoCartorial(); imovelCtrl.showUploadCertidaoCartorialDialog($event)\" aria-label=\"upload certidão cartorial\"><md-tooltip md-direction=bottom>Documento emitido pelo cartório de registro de imóveis contendo a indicação da matrícula/transcrição e do registro de aquisição em nome da união ou da entidade federal proprietária.</md-tooltip>Adicionar documento(s)</md-button></div><div layout=row><input type=hidden class=secaoDadosIncorporacao ng-model=imovel.certidoesCartorial ng-required=\"imovelCtrl.dataMatriculaIsRequired && imovelCtrl.matriculaIsRequired\" name=certidaoCartorial> <span ng-show=\"imovel.incorporacao.dataMatricula && imovel.incorporacao.numeroMatricula && imovel.certidoesCartorial.length === 0\" class=form-error>* Necessário enviar pelo menos uma certidão cartorial</span></div></div><div layout=row ng-show=\"imovel.certidoesCartorial.length > 0\" flex><md-content flex><h4>Documentos cartoriais</h4><table class=mui-table style=\"width:100% !important\"><thead><tr><th style=\"width: 200px !important\">Nome da certidão</th><th>Data da emissão</th><th>Nome do arquivo</th><th>Ação</th></tr></thead><tbody><tr ng-repeat=\"item in imovel.certidoesCartorial\" ng-model-options=\"{trackBy: \'$value.id\'}\"><td>{{item.nome}}</td><td>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td>{{item.nomeArquivo}}</td><td><ng-md-icon icon=search style=fill:#788489 size=18 alt=\"Visualizar documento\" ng-click=visualizarCertidao(item.id)></ng-md-icon><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=imovelCtrl.removerCertidao($index)></ng-md-icon></td></tr></tbody></table></md-content></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/imovel-terreno.tmpl.html","<h1>Cadastrar Imóvel</h1><md-divider></md-divider><form name=formGeral class=noPadding><div layout=column layout-xs=column><div ng-cloak flex=100 flex-xs=100><md-content><md-tabs md-border-bottom md-dynamic-height ng-show=\"!imovelCtrl.isInclusaoBenfeitoria && !imovelCtrl.mostraTabTituloAquisitivo && !imovelCtrl.mostraTabFormDocumento\"><md-tab ng-repeat=\"tab in imovelCtrl.tabs.tabs\" label={{tab.nome}} ng-disabled=\"tab.index != imovelCtrl.tabs.selectedIndex\" md-selected=imovelCtrl.tabs.selectedIndex ng-click=imovelCtrl.validaTab(tab.index)><div ng-include=tab.template></div></md-tab></md-tabs><md-tabs md-border-bottom md-dynamic-height ng-if=imovelCtrl.isInclusaoBenfeitoria><md-tab label={{imovelCtrl.tabs.tabInclusaoBenfeitoria.nome}}><inclusao-benfeitoria benfeitoria=imovelCtrl.benfeitoria imovel=imovelCtrl.imovel show-inclusao-benfeitoria=imovelCtrl.isInclusaoBenfeitoria show-tab-form-documento=imovelCtrl.mostraTabFormDocumento documento=imovelCtrl.documentoBenfeitoria></inclusao-benfeitoria></md-tab></md-tabs><md-tabs md-border-bottom md-dynamic-height ng-if=imovelCtrl.mostraTabTituloAquisitivo><md-tab label={{imovelCtrl.tabs.tabInclusaoTituloAquisitivo.nome}}><titulo-aquisitivo action=detail titulo=imovelCtrl.titulo titulo-backup=imovelCtrl.tituloBackup imovel=imovelCtrl.imovel show-titulo-aquisitivo=imovelCtrl.mostraTabTituloAquisitivo query-pagination=imovelCtrl.paginationTituloAquisitivo></titulo-aquisitivo><div ng-include=imovelCtrl.tabs.tabInclusaoTituloAquisitivo.template></div></md-tab></md-tabs><md-tabs md-border-bottom md-dynamic-height ng-if=\"imovelCtrl.mostraTabFormDocumento && (imovelCtrl.imovel.tipoImovel.tipoPai === \'UNIDADE AUTÔNOMA\')\"><md-tab label={{imovelCtrl.tabs.tabFormDocumento.nome}}><form-documento documento=imovelCtrl.documento documento-backup=imovelCtrl.documentoBackup is-benfeitoria=false show-form-documento=imovelCtrl.mostraTabFormDocumento documentos=imovelCtrl.imovel.unidadeAutonoma.edificacao.documentacao></form-documento></md-tab></md-tabs><md-tabs md-border-bottom md-dynamic-height ng-if=\"imovelCtrl.mostraTabFormDocumento && (imovelCtrl.imovel.tipoImovel.id == 2 || imovelCtrl.imovel.tipoImovel.id == 3)\"><md-tab label={{imovelCtrl.tabs.tabFormDocumento.nome}}><form-documento documento=imovelCtrl.documentoBenfeitoria show-form-documento=imovelCtrl.mostraTabFormDocumento is-benfeitoria=true show-inclusao-benfeitoria=imovelCtrl.isInclusaoBenfeitoria documentos=imovelCtrl.benfeitoria.edificacao.documentacao></form-documento></md-tab></md-tabs></md-content></div></div></form>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/inserir-imovel.tmpl.html","<div layout=row><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=0 edificacoes=imovelCtrl.edificacoesUnidadeAutonoma imovel=imovel action=add habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout-xs=column flex=70><md-content flex><ng-form name=forms.imovelForm><v-accordion multiple class=vAccordion--default><v-pane id=detalhesDoImovel expanded><v-pane-header>Detalhes do Imóvel</v-pane-header><v-pane-content id=detalhesDoImovelContent><secao-detalhes-imovel imovel=imovel is-detail=false></secao-detalhes-imovel></v-pane-content></v-pane><v-pane id=dadosIncorporacao><v-pane-header>Dados de Incorporação</v-pane-header><v-pane-content id=dadosIncorporacaoContent><secao-dados-incorporacao ng-if=imovelCtrl.usuarioLog imovel=imovel is-exibe-tab-titulo-aquisitivo=imovelCtrl.mostraTabTituloAquisitivo titulo=imovelCtrl.titulo titulo-backup=imovelCtrl.tituloBackup query-pagination=imovelCtrl.paginationTituloAquisitivo></secao-dados-incorporacao></v-pane-content></v-pane><v-pane><v-pane-header>Classificação de Utilização</v-pane-header><v-pane-content><secao-classificacao-utilizacao imovel=imovel is-detail=false></secao-classificacao-utilizacao></v-pane-content></v-pane><v-pane><v-pane-header>Fotos do Imóvel</v-pane-header><v-pane-content><upload-fotos model=imovel.imagens></upload-fotos></v-pane-content></v-pane></v-accordion><div class=separador></div><div layout=row layout-align=\"end center\"><md-button md-no-ink class=\"md-secondary md-raised\" ng-click=selectTab(-1)>Retornar</md-button><md-button class=\"md-secondary md-raised\" ng-click=imovelCtrl.limparImovel(2)>Limpar</md-button><md-button class=\"md-primary md-raised\" ng-click=imovelCtrl.submitFormularioDadosGerais()>AVANÇAR</md-button></div></ng-form></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/inserir-localizacao.tmpl.html","<div layout-xs=column ng-controller=\"LocalizacaoController as ctrl\"><md-content flex><ng-form name=forms.localizacaoForm><inc-localizacao-imovel ng-if=imovelCtrl.usuarioLog imovel=imovelCtrl.imovel edificacoes-unidade-autonoma=imovelCtrl.edificacoesUnidadeAutonoma action=\"\'add\'\" hints=imovelCtrl.camposAjuda></inc-localizacao-imovel></ng-form><div class=separador></div><div layout=row layout-align=\"end center\"><md-button class=\"md-secondary md-raised\" ng-click=imovelCtrl.limparImovel(-1)>Limpar</md-button><md-button class=\"md-primary md-raised\" ng-click=imovelCtrl.submitFormularioLocalizacao()>Avançar</md-button></div></md-content></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/inserir-titulo-aquisitivo.html","<div layout=row layout-xs=column><div layout=column flex=70 flex-xs=100 style=\"padding: 0 10px\"><ng-form name=\"\"><md-input-container ng-class=\"\" class=md-block flex><label>Tipo</label><md-select class=secaoDadosIncorporacao ng-model=imovel.tituloAquisitivo.tipo ng-model-options=\"{trackBy: \'$value.id\'}\" required name=tipoTituloAquisitivo><md-option ng-repeat=\"item in imovelCtrl.tiposTituloAquisitivo\" ng-value=item ng-selected=\"item.id === imovelCtrl.valorTipoTituloAquisitivo\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.imovelForm.tipoAquisicao.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=md-block flex ng-show=\"imovel.incorporacao.tipoIncorporacao.id === 2\"><label>Tipo de Ato Legal</label><md-select class=secaoDadosIncorporacao ng-model=imovel.incorporacao.tipoAquisicao ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"imovel.incorporacao.tipoIncorporacao.id === 2\" name=tipoAquisicao><md-option ng-repeat=\"item in imovelCtrl.tiposAquisicao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></ng-form></div></div><div layout=row layout-align=\"end center\"><!--<md-button class=\"md-primary md-raised\" ng-click=\"imovelCtrl.cancelarInclusaoTitulo();\">\n        Fechar\n    </md-button>\n\n    <md-button class=\"md-raised\" ng-click=\"imovelCtrl.limparInclusaoTitulo();\">\n        Limpar\n    </md-button>\n\n    <md-button md-no-ink class=\"md-primary md-raised\" ng-click=\"imovelCtrl.salvarTituloAquisitivo();\">\n        Gravar\n    </md-button>--></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/matricula-transcricao-fieldset.html","<fieldset class=fieldset><style>table.md-table:not(.md-row-select)\n      td.md-cell:nth-child(n + 2):nth-last-child(n + 2) {\n      padding: 2px 8px;\n    }\n\n    table:not(.md-row-select)\n      th.md-column:nth-child(n + 2):nth-last-child(n + 2) {\n      padding: 2px 8px;\n    }\n\n    table.md-table:not(.md-row-select)\n      td.md-cell:nth-child(n + 2):nth-last-child(n + 2),\n    table.md-table:not(.md-row-select)\n      th.md-column:nth-child(n + 2):nth-last-child(n + 2) {\n      padding: 2px 8px;\n    }</style><legend><label style=\"color: rgb(76, 175, 80)\">Matrícula/Transcrição</label></legend><div layout-gt-xs=row ng-if=\"!vm.isDetail && !vm.isEdit\"><!-- UF --><md-input-container class=md-block flex-gt-sm flex ng-show=\"imovel.enderecamento.pais.id === 1\"><label>UF*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=matricula.uf ng-model-options=\"{trackBy: \'$value\'}\" name=ufSelect placeholder=UF* ng-change=pesquisaMunicipio(matricula.uf)><md-option ng-repeat=\"uf in imovelCtrl.ufs\" ng-value=uf.sigla>{{uf.sigla}}</md-option></md-select></md-input-container><!-- Município --><md-input-container class=md-block flex-gt-sm flex ng-show=\"imovel.enderecamento.pais.id === 1\"><label>Município*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=matricula.municipio ng-model-options=\"{trackBy: \'$value.codigo\'}\" name=municipio placeholder=Município* ng-change=\"imovelCtrl.carregarCartorios(matricula.uf, matricula.municipio.codigo)\"><md-option ng-repeat=\"municipio in municipios | filter:nome | orderBy:\'nome\'\" ng-value=municipio>{{municipio.nome}}</md-option></md-select></md-input-container><!-- Nome do Cartório --><md-input-container class=md-block flex-gt-sm flex ng-show=imovel.isPaisBrasil(); onkeyup=maiuscula(this)><label>Nome do Cartório*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=matricula.cartorio ng-model-options=\"{trackBy: \'$value.id\'}\" name=cartorio placeholder=\"Nome do Cartório*\"><md-option ng-repeat=\"cartorio in imovelCtrl.cartorios | filter:nome | orderBy:\'nome\'\" ng-value=cartorio>{{cartorio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-sm flex ng-show=!imovel.isPaisBrasil();><label>Identificação do Cartório</label><textarea class=secaoDadosLocalizacaoImovel ng-model=matricula.cartorioEstrangeiro md-maxlength=400></textarea></md-input-container></div><!-- Linha das Datas --><div layout-gt-xs=row class=layout-gt-xs-row><md-input-container class=flex-gt-xs flex-gt-xs ng-class=\"forms.imovelForm.numeroMatricula.$error.required && submittedImovel? \'md-input-invalid\': null\"><label>Matrícula/Transcrição*</label><input class=secaoDadosIncorporacao ng-model=matricula.numero name=numeroMatricula></md-input-container><div flex-gt-xs class=flex-gt-xs><label style=\"margin-left: 16px\" class=matriculas-cs-data-label>Data da Matrícula *<span ng-show=\"imovelCtrl.dataMatriculaIsRequired && imovelCtrl.validaCamposConformeTipoImovel()  && imovelCtrl.imovel.incorporacao.tipoIncorporacao.id !== 5\">*</span></label><md-datepicker ng-mask=39/19/9999 restrict=reject class=\"secaoDadosIncorporacao matricula-cs\" ng-model=matricula.dataMatricula md-placeholder=\"Data da matrícula\" md-max-date=imovelCtrl.hoje name=dataMatricula></md-datepicker></div><div flex-gt-xs class=flex-gt-xs><label style=\"margin-left: 20px\" class=matriculas-cs-data-label>Data de Registro<span ng-show=\"imovelCtrl.dataRegistroIsRequired && imovelCtrl.validaCamposConformeTipoImovel() && imovelCtrl.imovel.incorporacao.tipoIncorporacao.id !== 5\" *></span></label><md-datepicker ng-mask=39/19/9999 restrict=reject class=\"secaoDadosIncorporacao matricula-cs\" ng-model=matricula.dataRegistro md-placeholder=\"Data de registro\" md-max-date=imovelCtrl.hoje name=dataRegistro></md-datepicker></div></div><div class=mgt-50 layout=row layout-align=\"end center\" ng-if=\"!vm.isDetail && !vm.isEdit\"><md-button class=\"md-primary md-raised\" ng-click=adicionaMatricula(matricula) ng-show=\"matricula.index !== undefined\">Alterar</md-button><md-button class=\"md-primary md-raised\" ng-click=adicionaMatricula(matricula) ng-show=\"matricula.index === undefined\">Adicionar</md-button></div><!-- Tabela Template --><div class=mgt-25 layout-gt-xs=row layout-align=center ng-show=imovelCtrl.hasMatricula() ng-if=!vm.isDetail><md-content><md-table-container ng-class=matricula-cs><table md-table md-progress=promise><thead md-head><tr md-row><th md-column class=inc-text-center style=\"width: 150px\">Localização</th><th md-column class=inc-text-center style=\"width: 200px; text-align: center !important\">Cartório</th><th md-column class=inc-text-center style=\"width: 70px\">Matrícula/Transcrição</th><th md-column class=inc-text-center style=\"width: 50px\">Data da Matrícula</th><th md-column class=inc-text-center style=\"width: 50px\">Data do Registro</th><th md-column class=inc-text-center style=\"width: 100px\"></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"matricula in imovel.incorporacao.matriculas | limitTo: tabelaMatricula.limit: (tabelaMatricula.page - 1) * tabelaMatricula.limit\"><td md-cell class=inc-text-center ng-show=\"imovel.enderecamento.pais.id === 1\">{{matricula.uf}}/{{matricula.municipio}}</td><td md-cell class=inc-text-center ng-show=\"!imovel.enderecamento.pais.id === 1\">{{matricula.cidadeExterior}}</td><td md-cell class=inc-text-center ng-show=\"imovel.enderecamento.pais.id === 1\">{{matricula.cartorio.nome}}</td><td md-cell class=inc-text-center ng-show=\"!imovel.enderecamento.pais.id === 1\">{{matricula.cartorioEstrangeiro}}</td><td md-cell class=inc-text-center>{{matricula.numero}}</td><td md-cell class=inc-text-center>{{matricula.dataMatricula | date:\'dd/MM/yyyy\'}}</td><td md-cell class=inc-text-center>{{matricula.dataRegistro | date:\'dd/MM/yyyy\'}}</td><td md-cell class=inc-text-center><div layout=row layout-align=\"center stretch\"><md-button class=\"md-icon-button matricula-cs\" alt=\"Editar Matrícula\"><ng-md-icon icon=create style=\"fill: #788489\" ng-click=alteraMatricula(matricula)><md-tooltip>Editar Matrícula</md-tooltip></ng-md-icon></md-button><md-button class=\"md-icon-button matricula-cs\" alt=\"Remover Matrícula\"><ng-md-icon icon=delete style=\"fill: #788489\" ng-click=confirmaRemocao(matricula)><md-tooltip>Remover Matrícula</md-tooltip></ng-md-icon></md-button></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabelaMatricula.limit md-limit-options=tabelaMatricula.limitsPage md-page=tabelaMatricula.page md-total={{imovel.incorporacao.matriculas.length}} md-on-paginate=true></md-table-pagination></md-content></div><!-- Detalhar e Alterar --><div layout-gt-xs=row ng-show=vm.isEdit><!-- UF --><md-input-container class=md-block flex-gt-sm flex ng-show=\"vm.imovel.enderecamento.pais.id === 1\"><label>UF*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=vm.matricula.uf ng-model-options=\"{trackBy: \'$value\'}\" name=ufSelect placeholder=UF* ng-change=vm.pesquisaMunicipio(vm.matricula.uf)><md-option ng-repeat=\"uf in vm.ufs\" ng-value=uf.sigla>{{uf.sigla}}</md-option></md-select></md-input-container><!-- Município --><md-input-container class=md-block flex-gt-sm flex ng-show=\"vm.imovel.enderecamento.pais.id === 1\"><label>Município*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=vm.matricula.municipio ng-model-options=\"{trackBy: \'$value.codigo\'}\" name=municipio placeholder=Município* ng-change=\"vm.carregarCartorios(vm.matricula.uf, vm.matricula.municipio.codigo)\"><md-option ng-repeat=\"municipio in vm.municipios | filter:nome | orderBy:\'nome\'\" ng-value=municipio>{{municipio.nome}}</md-option></md-select></md-input-container><!-- Nome do Cartório --><md-input-container class=md-block flex-gt-sm flex ng-show=\"vm.imovel.enderecamento.pais.id === 1\"><label>Nome do Cartório*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=vm.matricula.cartorio ng-model-options=\"{trackBy: \'$value.id\'}\" name=cartorio placeholder=\"Nome do Cartório*\"><md-option ng-repeat=\"cartorio in vm.cartorios | filter:nome | orderBy:\'nome\'\" ng-value=cartorio>{{cartorio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-sm flex ng-show=\"!vm.imovel.enderecamento.pais.id === 1\"><label>Identificação do Cartório</label><textarea class=secaoDadosLocalizacaoImovel ng-model=vm.matricula.cartorioEstrangeiro md-maxlength=400></textarea></md-input-container></div><!-- Linha das Datas --><div layout-gt-xs=row class=layout-gt-xs-row ng-show=vm.isEdit><md-input-container class=flex-gt-xs flex-gt-xs ng-class=\"forms.imovelForm.numeroMatricula.$error.required && submittedImovel? \'md-input-invalid\': null\"><label>Matrícula/Transcrição*</label><input class=secaoDadosIncorporacao ng-model=vm.matricula.numero name=numeroMatricula></md-input-container><div flex-gt-xs class=flex-gt-xs><label style=\"margin-left: 16px\" class=matriculas-cs-data-label>Data da Matrícula *<span ng-show=\"vm.dataMatriculaIsRequired && vm.validaCamposConformeTipoImovel()  && vm.imovel.incorporacao.tipoIncorporacao.id !== 5\">*</span></label><md-datepicker ng-mask=39/19/9999 restrict=reject class=\"secaoDadosIncorporacao matricula-cs\" ng-model=vm.matricula.dataMatricula md-placeholder=\"Data da matrícula\" md-max-date=vm.hoje name=dataMatricula></md-datepicker></div><div flex-gt-xs class=flex-gt-xs><label style=\"margin-left: 20px\" class=matriculas-cs-data-label>Data de Registro<span ng-show=\"vm.dataRegistroIsRequired && vm.validaCamposConformeTipoImovel() && vm.imovel.incorporacao.tipoIncorporacao.id !== 5\">*</span></label><md-datepicker ng-mask=39/19/9999 restrict=reject class=\"secaoDadosIncorporacao matricula-cs\" ng-model=vm.matricula.dataRegistro md-placeholder=\"Data de registro\" md-max-date=vm.hoje name=dataRegistro></md-datepicker></div></div><div class=mgt-50 layout=row layout-align=\"end center\" ng-if=vm.isEdit><md-button class=\"md-primary md-raised\" ng-click=vm.adicionaMatricula(vm.matricula) ng-show=\"vm.matricula.index !== undefined\">Alterar</md-button><md-button class=\"md-primary md-raised\" ng-click=vm.adicionaMatricula(vm.matricula) ng-show=\"vm.matricula.index === undefined\">Adicionar</md-button></div><!-- Tabela Detail e Edit --><div class=mgt-25 layout-gt-xs=row layout-align=center ng-show=true ng-if=\"vm.isDetail || vm.isEdit\"><md-content><md-table-container ng-class=matricula-cs><table md-table md-progress=promise><thead md-head><tr md-row><th md-column class=inc-text-center style=\"width: 150px\">Localização</th><th md-column class=inc-text-center style=\"width: 200px; text-align: center !important\">Cartório</th><th md-column class=inc-text-center style=\"width: 70px\">Matrícula/Transcrição</th><th md-column class=inc-text-center style=\"width: 50px\">Data da Matrícula</th><th md-column class=inc-text-center style=\"width: 50px\">Data do Registro</th><th ng-if=vm.isEdit md-column class=inc-text-center style=\"width: 100px\"></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"matricula in vm.imovel.incorporacao.matriculas | limitTo: tabelaMatricula.limit: (tabelaMatricula.page - 1) * tabelaMatricula.limit\"><td md-cell class=inc-text-center ng-show=\"vm.imovel.enderecamento.pais.id === 1\">{{matricula.uf}}/{{matricula.municipio}}</td><td md-cell class=inc-text-center ng-show=\"!vm.imovel.enderecamento.pais.id === 1\">{{matricula.cidadeExterior}}</td><td md-cell class=inc-text-center ng-show=\"vm.imovel.enderecamento.pais.id === 1\">{{matricula.cartorio.nome}}</td><td md-cell class=inc-text-center ng-show=\"!vm.imovel.enderecamento.pais.id === 1\">{{matricula.cartorioEstrangeiro}}</td><td md-cell class=inc-text-center>{{matricula.numero}}</td><td md-cell class=inc-text-center>{{matricula.dataMatricula | date:\'dd/MM/yyyy\'}}</td><td md-cell class=inc-text-center>{{matricula.dataRegistro | date:\'dd/MM/yyyy\'}}</td><td md-cell class=inc-text-center ng-if=vm.isEdit><div layout=row layout-align=\"center stretch\"><md-button class=\"md-icon-button matricula-cs\" alt=\"Editar Matrícula\"><ng-md-icon icon=create style=\"fill: #788489\" ng-click=vm.alteraMatricula(matricula)><md-tooltip>Editar Matrícula</md-tooltip></ng-md-icon></md-button><md-button class=\"md-icon-button matricula-cs\" alt=\"Remover Matrícula\"><ng-md-icon icon=delete style=\"fill: #788489\" ng-click=vm.confirmaRemocao(matricula)><md-tooltip>Remover Matrícula</md-tooltip></ng-md-icon></md-button></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabelaMatricula.limit md-limit-options=tabelaMatricula.limitsPage md-page=tabelaMatricula.page md-total={{vm.imovel.incorporacao.matriculas.length}} md-on-paginate=true></md-table-pagination></md-content></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/pesquisa-cartorio.tmpl.html","<md-dialog aria-label=\"Seleção do Cartorio\" class=dialog-cartorio><ng-form name=forms.cartorioForm><md-toolbar><div class=md-toolbar-tools><h2>Seleção do cartório</h2><span flex></span><md-button class=md-icon-button ng-click=cancel()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=\"Fechar caixa de diálogo\"></md-icon></md-button></div></md-toolbar><md-dialog-content style=max-width:800px;max-height:810px><md-input-container class=md-block flex><label>UF</label><input ng-model=ufSelecionada ng-disabled=true> <span class=form-error ng-show=\"submittedCartorio && !ufSelecionada\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container class=md-block flex><label>Munícipio</label><input ng-model=municipioSelecionado ng-disabled=true> <span class=form-error ng-show=\"submittedCartorio && !municipioSelecionado\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container class=md-block flex><label>Nome do Cartório</label><md-select ng-model=cartorioSelecionado ng-disabled=\"municipioSelecionado === undefined\" name=nomeCartorio><md-option ng-repeat=\"cartorio in cartorios\" ng-value=cartorio ng-model-options=\"{trackBy: \'$value.id\'}\">{{cartorio.nome}}</md-option></md-select><span class=form-error ng-show=\"submittedCartorio && !cartorioSelecionado\">Campo de preenchimento obrigatório</span></md-input-container></md-dialog-content><md-dialog-actions layout=row><md-button ng-click=cancel() class=\"md-secondary md-raised\">Fechar</md-button><md-button type=submit ng-click=\"selecionaCartorio(cartorioSelecionado, forms.cartorioForm.$invalid, $event);\" style=margin-right:20px class=\"md-raised md-primary\">Selecionar</md-button></md-dialog-actions></ng-form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/pesquisar-imovel.tmpl.html","<div layout=column><ng-form name=forms.pesquisaForm><div layout=row ui-view></div><div layout=row><div layout=column flex><div class=\"tamanho-container margem-horizontal\" layout=row><md-input-container class=\"md-block margem-baixa-padrao\" flex-gt-sm ng-show=\"piCtrl.modoExibicao.id == 1\"><label>Ordenar por</label><md-select campo-ajuda=INC_FORM_PESQ_IMOVEL_ORDEN_POR ng-model=piCtrl.ordenarPor name=ordenarPor><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in piCtrl.ordenarPorOpt | orderByBr:\'descricao\'\" ng-value=item.valor ng-selected=\"item.id == 3\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-sm><label>Modo de exibição</label><md-select campo-ajuda=INC_FORM_PESQ_IMOVEL_MOD_EXIB ng-model=piCtrl.modoExibicao name=modoExibicao><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in piCtrl.modoExibicaoOpt | orderByBr:\'descricao\'\" ng-value=item ng-selected=\"item.id == 1\">{{item.descricao}}</md-option></md-select></md-input-container></div><div class=\"separador margem-horizontal\"></div><div class=margem-horizontal-botoes layout=row layout-align=\"end center\"><md-button class=\"md-raised md-secondary\" ng-click=piCtrl.limpar()>Limpar</md-button><md-button class=\"md-raised md-primary\" ng-click=piCtrl.pesquisar()>Pesquisar</md-button></div></div></div><div layout=row layout-align=\"center stretch\" ng-show=\"piCtrl.resultadoPesquisa.length > 0 && !piCtrl.exibirResultadoMapa\" ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/pesquisar-imovel/lista-imoveis.tmpl.html\'\"></div></ng-form></div><div><div class=mapaLocalizacao ng-if=\"piCtrl.modoExibicao.descricao === \'Mapa\'\"><ci-mapa-incorporacao showminimapa=false ng-show=piCtrl.exibirResultadoMapa tabindex=\"\" imovel=\"\" action=pesquisarImovel habilitar-desenho=false></ci-mapa-incorporacao></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/proprietario.tmpl.html","<div layout=row><div layout=column flex=30><div class=div-cimapa id=fotoPontoImovel><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=5 imovel=imovel action=add habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout-xs=column flex=70><md-content flex><ng-form name=forms.proprietarioForm><v-accordion multiple class=vAccordion--default style=\"margin-top: 20px\"><v-pane id=secaoProprietario expanded><v-pane-header>Dados do Proprietário/Titular</v-pane-header><v-pane-content id=secaoProprietarioContent><dados-proprietario ng-if=imovelCtrl.usuarioLog imovel=imovel is-detail=false></dados-proprietario></v-pane-content></v-pane></v-accordion></ng-form><div layout=row layout-align=\"end center\"><md-button md-no-ink class=\"md-secondary md-raised\" ng-click=imovelCtrl.tabs.previousTab();>Retornar</md-button><md-button class=\"md-secondary md-raised\" ng-click=imovelCtrl.limparImovel(5)>Limpar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=\"showModalValidacao($event, forms.proprietarioForm.$invalid, 5, \'salvar\')\">Gravar</md-button></div></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/resultado-homologacao.html","<div layout=column><div layout=column><p><span style=\"font-weight: bold\">Situação: </span>Cadastro/Edição de imóvel recusada na homologação</p><p><span style=\"font-weight: bold\">Data: </span>{{ vm.imovel.imovelRecusado.data }}</p><p><span style=\"font-weight: bold\">Homologador: </span>{{ vm.imovel.imovelRecusado.homologador }}</p><p><span style=\"font-weight: bold\">Observação: </span>{{ vm.imovel.imovelRecusado.observacao }}</p></div><div layout=row layout-align=\"end center\"><md-button class=\"md-secondary md-raised\" ng-click=vm.detalharHomologacao()>Ver detalhamento da homologação</md-button><md-button class=\"md-primary md-raised\" ng-click=selectTab(-1)>Avançar</md-button></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/mapa/view/mapa-pesquisa.html","<openlayers id=mapaPesquisa></openlayers>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/view/pendencia-imovel-listar.tmpl.html","<div layout=column layout-align=\"start center\" ng-show=\"consultarPendenciaCtrl.resultadoPesquisa.length > 0\"><div class=tabela-pesquisa-imovel><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column>Ação Executada</th><th md-column>Responsável pela Ação</th><th md-column>Imóvel</th><th md-column>Detalhes</th><th md-column>Ação</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in consultarPendenciaCtrl.resultadoPesquisa\"><td md-cell style=\"text-align: center\"><p>{{item.dsAcaoExecutada}}</p></td><td md-cell style=\"text-align: center\"><p>{{item.dsNomeUsuarioAcaoExecutada}}</p></td><td md-cell style=\"text-align: center; height: 170px\"><img ng-src=\"{{item.imagens[0].imagem.arquivo != undefined ? \'data:image/jpeg;base64,\'+item.imagens[0].imagem.arquivo :\'./assets/img/no-image.png\'}}\"></td><td md-cell><p ng-show=item.ripValido><span style=\"font-weight: bold\">RIP:</span> {{item.rip}}</p><p ng-show=!item.ripValido><span style=\"font-weight: bold\">RIP:</span> -</p><p ng-show=\"item.enderecamento.pais.descricao == \'Brasil\'\"><span style=\"font-weight: bold\">Endereço:</span> {{item.enderecamento.logradouro}}, Número {{item.numero}}, {{item.enderecamento.bairro}}, {{item.enderecamento.municipio}}/{{item.enderecamento.uf}}-{{item.enderecamento.pais.descricao}}. CEP: {{item.enderecamento.cep}}</p><p ng-show=\"item.enderecamento.pais.descricao != \'Brasil\'\"><span style=\"font-weight: bold\">Endereço:</span> {{item.enderecamento.endereco}}, {{item.complemento}}, {{item.enderecamento.cidadeExterior}} - {{item.enderecamento.pais.descricao}}. Código Postal: {{item.enderecamento.codigoPostal}}.</p><p><span style=\"font-weight: bold\">Situação:</span> {{item.situacaoImovel.descricao}}</p><p><span style=\"font-weight: bold\">Data de cadastramento:</span> {{item.dataCadastro | date : \'dd/MM/yyyy\'}}</p><p><span style=\"font-weight: bold\">Proprietário/Titular:</span> {{item.proprietario.tipoProprietarioReal.descricao}}</p><p ng-show=\"item.incorporacao.numeroMatricula != undefined\"><span style=\"font-weight: bold\">Matrícula/Trascrição:</span> {{item.incorporacao.numeroMatricula}}</p><p ng-show=\"item.incorporacao.numeroMatricula == undefined\"><span style=\"font-weight: bold\">Matrícula/Trascrição:</span> Não informado</p><p ng-show=\"!!item.pendencias && item.pendencias.length > 0\" style=\"color: red\"><span style=\"font-weight: bold\">Descrição da pendência:</span> {{consultarPendenciaCtrl.getTituloPendencia(item)}}</p></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=consultarPendenciaCtrl.verLocalizacao($event,item) class=\"md-fab md-raised md-mini\" aria-label=\"Ver Localização\"><md-icon ng-bind=\"\'map\'\"><md-tooltip>Ver Localização</md-tooltip></md-icon></md-button><md-button ng-click=consultarPendenciaCtrl.irParaDetalharImovel(item.id) class=\"md-fab md-raised md-mini\" aria-label=\"Detalhar Imóvel para Homologação\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Detalhar Imóvel</md-tooltip></md-icon></md-button><md-button ng-click=consultarPendenciaCtrl.irParaEdicao(item.id) class=\"md-fab md-raised md-mini\" exibir-elemento-por-permissao=CAD_IMOVEL.CADASTRAR_IMOVEL ng-show=consultarPendenciaCtrl.exibeEdicao(item) aria-label=\"Editar Imóvel\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Imóvel</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=consultarPendenciaCtrl.paginacao.limit md-limit-options=consultarPendenciaCtrl.paginacao.limitOptions md-page=consultarPendenciaCtrl.paginacao.page md-total={{consultarPendenciaCtrl.paginacao.totalElements}} md-on-paginate=consultarPendenciaCtrl.page md-page-select ng-disabled=\"consultarPendenciaCtrl.paginacao.totalElements < 6\"></md-table-pagination><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" ng-click=consultarPendenciaCtrl.showLocalizacaoImovelDialog($event,consultarPendenciaCtrl.resultadoPesquisa)>Visualizar no Mapa</md-button></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/view/pendencia-imovel.tmpl.html","<style>/* Styles go here */\n  .md-chips {\n    width: 100%;\n    white-space: nowrap;\n    overflow-x: scroll;\n  }\n\n  /* Remove  .md-chips .md-chip-input-container\n      if you want the placehoder in a new line*/\n  .md-chips md-chip,\n  .md-chips .md-chip-input-container {\n    display: inline-block !important;\n    float: none !important;\n  }</style><div layout=column><h1>Consulta de Imóveis com Pendência</h1><md-divider></md-divider><ng-form name=consultarPendenciaImovelForm ng-keydown=\"$event.keyCode === 13 && consultarPendenciaCtrl.validarFormulario()\"><div class=\"tamanho-container primeiro-container margem-horizontal md-block\" layout=row><md-input-container class=md-block style=\"width: min-content\"><label>Tipo de RIP</label><md-select ng-model=consultarPendenciaCtrl.tipoRip ng-model-options=\"{trackBy: \'$value\'}\" required><md-option value=spunet ng-selected=true>RIP SPUnet</md-option><md-option value=spiunet>RIP SPIUnet</md-option></md-select></md-input-container><md-input-container class=\"md-block margem-direita-padrao\" flex=23 ng-if=\"consultarPendenciaCtrl.tipoRip === \'spunet\'\"><label>Número do RIP</label><input campo-ajuda=INC_FORM_CONSULTA_IMOVEL_N_RIP ng-model=consultarPendenciaCtrl.imovelPesquisa.rip ng-blur=consultarPendenciaCtrl.tratamentoRip()></md-input-container><md-input-container class=\"md-block margem-direita-padrao\" flex=23 ng-if=\"consultarPendenciaCtrl.tipoRip === \'spiunet\'\"><label>Número RIP SPIUnet</label><input campo-ajuda=INC_FORM_CONSULTA_IMOVEL_N_RIP ng-model=consultarPendenciaCtrl.imovelPesquisa.ripSpiunet></md-input-container><label style=\"margin-top: 25px\">Período de Cadastramento</label><md-input-container class=md-block flex=18 style=\"margin-right: inherit\"><md-datepicker class=datepicker-pendencia-imovel ng-mask=39/19/9999 restrict=reject name=periodoCadastramentoInicio campo-ajuda=INC_FORM_CONSULTA_IMOVEL_DT_INICIO ng-model=consultarPendenciaCtrl.imovelPesquisa.periodoCadastramentoInicio md-max-date=hoje></md-datepicker></md-input-container><md-input-container class=md-block flex=18 style=\"margin-right: 45px\"><md-datepicker class=datepicker-pendencia-imovel ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_CONSULTA_IMOVEL_DT_FIM ng-model=consultarPendenciaCtrl.imovelPesquisa.periodoCadastramentoFim md-max-date=hoje></md-datepicker></md-input-container><md-input-container class=\"md-block margem-direita-padrao\" flex=18><label>Matrícula do imóvel</label><input ng-model=consultarPendenciaCtrl.imovelPesquisa.matriculaImovel></md-input-container><md-input-container class=\"md-block margem-direita-padrao\" flex=18><label>Número do Processo</label><input ng-model=consultarPendenciaCtrl.imovelPesquisa.numeroProcesso></md-input-container></div><div class=\"tamanho-container margem-horizontal md-block\" layout=row style=\"height: 90px\"><md-input-container class=\"md-block margem-baixa-padrao\" flex=25><label>Proprietário titular</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_PROP_TIT multiple ng-model=consultarPendenciaCtrl.imovelPesquisa.idProprietario><md-option campo-ajuda={{proprietario.codigoSistemico}} ng-repeat=\"proprietario in consultarPendenciaCtrl.tiposTitular| orderByBr:\'descricao\'\" value={{proprietario.id}}>{{proprietario .descricao}}</md-option></md-select></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex=25 ng-show=\"consultarPendenciaCtrl.imovelPesquisa.idProprietario.includes(\'5\')\n                              || consultarPendenciaCtrl.imovelPesquisa.idProprietario.includes(\'6\')\n                              || consultarPendenciaCtrl.imovelPesquisa.idProprietario.includes(\'1\')\"><label>CPF/CNPJ do Proprietário</label><md-chips ng-model=consultarPendenciaCtrl.imovelPesquisa.cpfCnpjProprietarios name=cpfCnpjProprietarios placeholder=\"um ou mais CPF/CNPJ\" input-aria-label=\"CPF/CNPJ do Proprietário\" readonly md-removable=true md-enable-chip-edit=true md-add-on-blur=true md-transform-chip=consultarPendenciaCtrl.formataCpfCnpj($chip)><md-chip-template><strong>{{$chip}}</strong></md-chip-template></md-chips></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex=25><label>Ação Executada</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_PROP_TIT multiple ng-model=consultarPendenciaCtrl.imovelPesquisa.acaoExecutada><md-option ng-repeat=\"acao in consultarPendenciaCtrl.acoesExecutadas | orderByBr:\'dsAcaoExecutada\'\" value={{acao.cdAcaoExecutada}}>{{acao.dsAcaoExecutada}}</md-option></md-select></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex=25><label>Pendências</label><md-select campo-ajuda=INC_FORM_CONSULTA_IMOVEL_PROP_TIT multiple ng-model=consultarPendenciaCtrl.imovelPesquisa.pendencias ng-required=true><md-option ng-repeat=\"obj in consultarPendenciaCtrl.pendencias | orderByBr:\'pendencia\'\" ng-if=\"obj.pendencia !== \'Homologar Cadastro/Edição\'\" value={{obj.pendencia}} ng-selected=\"obj.pendencia === consultarPendenciaCtrl.pendenciaSelecionada\">{{obj.pendencia}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-sm flex=24><md-checkbox ng-model=consultarPendenciaCtrl.imovelPesquisa.exibirImoveisCadastradosEditadosDoUsuario>Apenas imóveis que eu Analisei</md-checkbox></md-input-container></div><div class=\"tamanho-container margem-horizontal md-block\" layout=row><md-input-container class=md-block flex><label>País</label><md-select campo-ajuda=INC_FORM_HOM_IMOVEL_PAIS ng-model=consultarPendenciaCtrl.imovelPesquisa.pais ng-change=consultarPendenciaCtrl.buscaCidadesPorPais() ng-model-options=\"{trackBy: \'$value\'}\" required><md-option ng-repeat=\"item in consultarPendenciaCtrl.paises | orderByBr:\'descricao\'\" ng-value=item.id ng-selected=\"item.id === 1\">{{ item.descricao }}</md-option></md-select></md-input-container><md-input-container class=md-block ng-show=\"consultarPendenciaCtrl.imovelPesquisa.pais === 1\" flex><label>CEP</label><input campo-ajuda=INC_FORM_HOM_IMOVEL_CEP ng-model=consultarPendenciaCtrl.imovelPesquisa.cep ui-br-cep-mask ng-blur=consultarPendenciaCtrl.buscaEnderecoPorCep()></md-input-container><md-input-container class=md-block ng-show=\"consultarPendenciaCtrl.imovelPesquisa.pais !== 1\" flex><md-select campo-ajuda=INC_FORM_HOM_IMOVEL_CID_EXTER name=selectSituacao ng-model=consultarPendenciaCtrl.imovelPesquisa.cidadeExterior placeholder=\"Selecione Cidade Exterior\"><md-option ng-repeat=\"cidade in consultarPendenciaCtrl.cidades\" value={{cidade}}>{{cidade}}</md-option></md-select></md-input-container><!--    </div>--><!--    <div layout=\"row\">--><md-input-container class=md-block ng-show=\"consultarPendenciaCtrl.imovelPesquisa.pais === 1\" flex><label id=labelUf>UF</label><!---\n                <md-select ng-disabled=\"consultarPendenciaCtrl.cepPreenchido && consultarPendenciaCtrl.cepValido\" ng-model=\"consultarPendenciaCtrl.imovelPesquisa.uf\" name=\"uf\" ng-required=\"consultarPendenciaCtrl.imovelPesquisa.pais === 1\">\n                    <md-option ng-repeat=\"uf in consultarPendenciaCtrl.ufs\" ng-value=\"uf.sigla\" ng-model-options=\"{trackBy: \'$value.id\'}\">\n                        {{ uf.descricao }}\n                    </md-option>\n                </md-select>\n            --><md-autocomplete ng-disabled=\"consultarPendenciaCtrl.cepPreenchido && consultarPendenciaCtrl.cepValido\" md-selected-item=consultarPendenciaCtrl.imovelPesquisa.uf name=uf ng-required=\"consultarPendenciaCtrl.imovelPesquisa.pais === 1 && !consultarPendenciaCtrl.imovelPesquisa.rip && !consultarPendenciaCtrl.imovelPesquisa.ripSpiunet\" md-clear-button=consultarPendenciaCtrl.apresentaBotao() md-selected-item-change=consultarPendenciaCtrl.pesquisaMunicipio(consultarPendenciaCtrl.imovelPesquisa.uf) md-search-text=$scope.searchUf md-no-cache md-items=\"item in consultarPendenciaCtrl.ufs | filter:{sigla:$scope.searchUf} | orderByBr:\'sigla\'\" md-item-text=item.sigla md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=labelUf><md-item-template><span md-highlight-text=$scope.searchUf md-highlight-flags=^i>{{item.sigla}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchUf}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container clas=md-block ng-show=\"consultarPendenciaCtrl.imovelPesquisa.pais === 1\" flex><label id=labelMunicipio>Município</label><!--\n                <input campo-ajuda=\"INC_FORM_HOM_IMOVEL_MUNICIPIO\"\n                ng-disabled=\"!consultarPendenciaCtrl.isCepGenerico && consultarPendenciaCtrl.cepValido\"\n                ng-model=\"consultarPendenciaCtrl.imovelPesquisa.municipio\">\n                --><md-autocomplete campo-ajuda=INC_FORM_HOM_IMOVEL_MUNICIPIO name=municipio md-no-cache=true md-selected-item-change=consultarPendenciaCtrl.selecionaMunicipio() ng-disabled=\"!consultarPendenciaCtrl.isCepGenerico && consultarPendenciaCtrl.cepValido\" md-clear-button=consultarPendenciaCtrl.apresentaBotao() md-selected-item=consultarPendenciaCtrl.imovelPesquisa.municipio md-search-text=$scope.searchMunicipio md-items=\"item in consultarPendenciaCtrl.municipios | filter:{nome:$scope.searchMunicipio} | orderByBr:\'nome\'\" md-item-text=item.nome md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=labelMunicipio><md-item-template><span md-highlight-text=$scope.searchMunicipio md-highlight-flags=^i>{{item.nome}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchMunicipio}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container></div><div layout=row layout-align=\"end center\"><md-button type=reset class=\"md-primary md-raised\" ng-click=consultarPendenciaCtrl.limpar()>Limpar</md-button><md-button class=\"md-primary md-raised\" ng-click=consultarPendenciaCtrl.validarFormulario()>Pesquisar</md-button></div></ng-form><div flex ng-include=\"\'scripts-cadastro-imovel/pages/pendencia/view/pendencia-imovel-listar.tmpl.html\'\"></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/suspensao/view/cadastroSuspenderImovel.tmpl.html","<h1 class=margem-horizontal>Cadastrar Suspensão de Imóvel</h1><md-divider class=margem-horizontal></md-divider><div layout=row layout-xs=column flex flex-xs=100><ng-form name=cadSuspensaoCtrl.forms.cadastroSuspenderImovel flex><div class=\"primeiro-container margem-horizontal tamanho-container\" layout=row style=\"height: auto; margin-bottom: 0\" flex=100><md-input-container class=\"md-block margem-direita-padrao\" flex=50><label>Tipo</label><md-select campo-ajuda=INC_FORM_CADAST_SUSPENSAO_TIPO ng-model=cadSuspensaoCtrl.suspensaoImovel.tipo name=tipo required><md-option campo-ajuda={{tipo.codigoSistemico}} ng-repeat=\"tipo in cadSuspensaoCtrl.tiposSuspensao | orderByBr:\'descricao\'\" ng-value=tipo>{{tipo.descricao}}</md-option></md-select></md-input-container><div flex=50><div layout=row><md-input-container class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" flex><md-radio-group name=processoSEI layout=row ng-model=cadSuspensaoCtrl.suspensaoImovel.tipoProcessoSei style=\"margin-left: 10px\" ng-change=cadSuspensaoCtrl.resetNumeroProcessoSuspensao() ng-required=true><md-radio-button value=processoExistente>Usar um processo SEI público existente aberto na sua unidade SPU</md-radio-button><md-radio-button value=processoExistenteNaoSPU>Informar um processo não existente no SEI da SPU (Não ocorrerá integração com SEI)</md-radio-button></md-radio-group></md-input-container></div><div layout=row><md-input-container class=md-block style=\"margin-left: 10px; margin-top: 4px; margin-bottom: -15px\" flex=50><label>Nº do Processo</label><input campo-ajuda=INC_FORM_CADAST_SUSPENSAO_N_PROCESSO name=numeroProcesso ng-model=cadSuspensaoCtrl.suspensaoImovel.numeroProcesso ng-required=true ng-disabled=\"!(cadSuspensaoCtrl.suspensaoImovel.tipoProcessoSei === \'processoExistente\' || cadSuspensaoCtrl.suspensaoImovel.tipoProcessoSei === \'processoExistenteNaoSPU\')\" ng-blur=cadSuspensaoCtrl.buscaProcessoSEISuspensao()></md-input-container><div style=\"padding-top: 10px\" flex=50><a ng-href={{cadSuspensaoCtrl.suspensaoImovel.linkProcessoSei}} target=_blank ng-show=\"cadSuspensaoCtrl.suspensaoImovel.linkProcessoSei !== null && cadSuspensaoCtrl.suspensaoImovel.linkProcessoSei !==  undefined\">Link do Processo no SEI</a> <a text-align=right; style=\"color: red;margin-bottom: 30px\" target=_blank ng-show=cadSuspensaoCtrl.exibeMengSei>Não foi possível integrar esse número de processo com o SEI. Isso não impede o cadastro da suspensão.</a></div></div></div></div><div class=\"tamanho-container margem-horizontal\" layout=row flex=100><md-input-container class=md-block flex><label>Descrição</label><textarea campo-ajuda=INC_FORM_CADAST_SUSPENSAO_DESCRICAO ng-model=cadSuspensaoCtrl.suspensaoImovel.descricao required name=descricao>\n	          </textarea></md-input-container></div><div class=\"tamanho-container margem-horizontal\" layout=row layout-xs=column flex=100><md-input-container class=\"md-block margem-direita-padrao\" flex=50><label>Motivação</label><md-select campo-ajuda=INC_FORM_CADAST_SUSPENSAO_MOTIVACAO ng-model=cadSuspensaoCtrl.suspensaoImovel.motivacao name=motivacao ng-required=true><md-option campo-ajuda={{motivacao.codigoSistemico}} ng-repeat=\"motivacao in cadSuspensaoCtrl.motivacoes | orderByBr:\'descricao\'\" ng-value=motivacao>{{motivacao.descricao}}</md-option></md-select></md-input-container><div layout=row layout-align=\"space-around stretch\" flex=50><md-input-container><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_CADAST_SUSPENSAO_INICIO_VIG flex required ng-model=cadSuspensaoCtrl.suspensaoImovel.vigencia name=vigencia placeholder=\"Início da Vigência\" md-min-date=cadSuspensaoCtrl.hoje inc-ajusta-tamanho></md-datepicker></md-input-container><md-input-container><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_CADAST_SUSPENSAO_FIM_VIG flex ng-model=cadSuspensaoCtrl.suspensaoImovel.dataFimVigencia placeholder=\"Final da Vigência\" name=dataFinalVigenciaSuspencao md-min-date=cadSuspensaoCtrl.amanha inc-ajusta-tamanho></md-datepicker></md-input-container></div></div><div class=\"tamanho-container margem-horizontal\" layout=row layout-xs=column flex=100><div layout=row layout-align=\"start start\" flex><md-input-container class=\"md-block margem-direita-padrao\" flex=50><label>RIP</label><textarea campo-ajuda=INC_FORM_CADAST_SUSPENSAO_RIP name=rip ng-model=cadSuspensaoCtrl.ripsConsulta ng-required=\"cadSuspensaoCtrl.ripsSelecionados.length == 0\" ng-blur=cadSuspensaoCtrl.tratamentoRip()></textarea></md-input-container><md-input-container flex=50><md-button ng-click=cadSuspensaoCtrl.incluirRip() class=\"md-raised md-primary botao-pos-input primeira-maiuscula\">Incluir</md-button>Não sabe o RIP,<nao-sei-rip text=\"Consulte aqui\" multiple-selection=true consulta=cadSuspensaoCtrl.ripsConsulta></nao-sei-rip></md-input-container></div></div><div ng-show=\"cadSuspensaoCtrl.ripsSelecionados.length > 0\" layout=row style=\"margin-bottom: 20px\"><md-chips ng-model=cadSuspensaoCtrl.ripsSelecionados readonly md-removable=true delete-hint=Remover flex><md-chip-template>{{$chip}}</md-chip-template></md-chips></div><div class=margem-horizontal layout=row><md-input-container class=md-block ng-controller=selectRestricaoController ng-cloak flex><label>Restrição</label><!--\n                <md-select campo-ajuda=\"INC_FORM_CADAST_SUSPENSAO_RESTRICAO\" required ng-model=\"cadSuspensaoCtrl.suspensaoImovel.restricoes\" name=\"restricao\" multiple>\n                    <md-optgroup label=\"Restrições\">\n                        <md-option campo-ajuda=\"{{item.codigoSistemico}}\" ng-value=\"item\" ng-repeat=\"item in cadSuspensaoCtrl.restricoes\">\n                            {{item.descricao}}\n                        </md-option>\n                    </md-optgroup>\n                </md-select>\n            --><md-select campo-ajuda=INC_FORM_CADAST_SUSPENSAO_RESTRICAO required multiple ng-model=cadSuspensaoCtrl.suspensaoImovel.restricoes name=restricao ng-model-options=\"{trackBy: \'$value.id\'}\" md-on-close=clearSearchRestricao() data-md-container-class=selectdemoSelectHeader><md-select-header class=demo-select-header><input id=inputRestricao name=inputRestricao ng-model=searchRestricao type=search placeholder=\"Informe o dado\" class=\"demo-header-searchbox md-text\" ng-click=$event.stopPropagation() ng-keydown=$event.stopPropagation()></md-select-header><md-optgroup label=restricão><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in cadSuspensaoCtrl.restricoes | filter:{descricao:searchRestricao} | orderByBr:\'descricao\'\" ng-disabled=\"item.descricao == \'Contabilidade\' || item.descricao == \'Destinação\' || item.descricao == \'Emissão de CAT\'\" ng-value=item>{{item.descricao}}</md-option></md-optgroup></md-select></md-input-container></div><div class=\"separador margem-horizontal\"></div><div layout=row class=margem-horizontal layout-align=\"end center\"><md-button class=\"md-primary md-raised\" ng-click=cadSuspensaoCtrl.goConsulta()>Fechar</md-button><md-button class=\"md-primary md-raised\" ng-click=cadSuspensaoCtrl.salvar($event)>Gravar</md-button></div></ng-form></div>");
$templateCache.put("scripts-cadastro-imovel/pages/suspensao/view/consultar-suspensao-imovel.tmpl.html","<div layout=column><h1 class=margem-horizontal>Pesquisa de Suspensão de Imóvel</h1><md-divider class=margem-horizontal></md-divider><ng-form name=consultaSuspensaoCtrl.forms.consultarSuspencaoImovel layout=column><div class=\"margem-horizontal primeiro-container\" layout=row><md-input-container class=\"md-block margem-direita-padrao\" flex><label>Tipo</label><md-select campo-ajuda=INC_FORM_CONSULTA_SUSPENSAO_TIPO ng-model=consultaSuspensaoCtrl.filtroConsulta.tipo name=tipo><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in consultaSuspensaoCtrl.tiposSuspensao | orderByBr:\'descricao\'\" ng-value=item.id>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex><label>Situação</label><md-select campo-ajuda=INC_FORM_CONSULTA_SUSPENSAO_SITUACAO ng-model=consultaSuspensaoCtrl.filtroConsulta.situacao><md-option campo-ajuda=INC_FORM_CONSULTA_SUSPENSAO_ATIVO value=ativo>Ativo</md-option><md-option campo-ajuda=INC_FORM_CONSULTA_SUSPENSAO_CANCEL value=cancelado>Cancelado</md-option></md-select></md-input-container></div><div layout=row class=margem-horizontal><md-input-container class=\"md-block margem-direita-padrao\" flex><label>Número do Processo</label><input campo-ajuda=INC_FORM_CONSULTA_SUSPENSAO_N_PROCESSO ng-model=consultaSuspensaoCtrl.filtroConsulta.numeroProcesso name=numeroProcesso md-maxlength=17 maxlength=17></md-input-container><md-input-container class=md-block flex><label>RIP</label><input campo-ajuda=INC_FORM_CONSULTA_SUSPENSAO_RIP ng-model=consultaSuspensaoCtrl.filtroConsulta.rip name=numeroRip md-maxlength=8 maxlength=8></md-input-container></div><div class=\"separador margem-horizontal\"></div><div layout=row class=margem-horizontal-botoes layout-align=\"end center\"><md-button class=\"md-raised md-primary\" ng-click=consultaSuspensaoCtrl.limpar()>Limpar</md-button><md-button class=\"md-raised md-primary\" ui-sref=INCORPORACAO.suspenderImovel>Novo</md-button><md-button class=\"md-primary md-raised\" ng-click=consultaSuspensaoCtrl.pesquisar()>Pesquisar</md-button></div></ng-form><div layout=row layout-align=\"center start\" style=\"margin-top: 20px\" ng-show=\"consultaSuspensaoCtrl.resultadoConsulta.length > 0\"><div flex=90><md-table-container><table md-table md-progress=promise><thead md-head md-order=consultaSuspensaoCtrl.ordenacao><tr md-row><th md-column md-numeric md-order-by=numeroProcesso>Processo</th><th md-column md-order-by=tipo.descricao>Tipo</th><th md-column md-order-by=ativo>Situação</th><th md-column>Ação</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in consultaSuspensaoCtrl.resultadoConsulta | orderBy: consultaSuspensaoCtrl.ordenacao\"><td md-cell><p>{{ item.numeroProcesso || \"-\" }}</p></td><td md-cell><p>{{ item.tipo.descricao }}</p></td><td md-cell><p>{{ item.ativo ? \"Ativo\" : \"Cancelado\" }}</p></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"consultaSuspensaoCtrl.showCancelarSuspensao($event, item)\" class=\"md-fab md-raised md-mini\" aria-label=\"Cancelar Suspensão\" ng-if=item.ativo><md-icon ng-bind=\"\'close\'\"><md-tooltip>Cancelar Suspensão</md-tooltip></md-icon></md-button><md-button ng-click=consultaSuspensaoCtrl.irParaDetalhamento(item.id) class=\"md-fab md-raised md-mini\" aria-label=\"Detalhar Suspensão\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Detalhar Suspensão</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=consultaSuspensaoCtrl.paginacao.limit md-limit-options=consultaSuspensaoCtrl.paginacao.limitOptions md-page=consultaSuspensaoCtrl.paginacao.page md-total=\"{{ consultaSuspensaoCtrl.paginacao.totalElements }}\" md-on-paginate=consultaSuspensaoCtrl.page md-page-select ng-disabled=\"consultaSuspensaoCtrl.paginacao.totalElements < 5\"></md-table-pagination></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/suspensao/view/detalhes-suspensao-imovel.tmpl.html","<div layout=column><h1>Detalhar Suspensão</h1><md-divider></md-divider><ng-form name=forms.suspensaoForm><div layout=row><md-input-container class=md-block flex><label>Tipo</label><input required ng-model=detalharSuspensaoCtrl.suspensao.tipo.descricao name=tipo ng-disabled=true></md-input-container><md-input-container class=md-block flex><label>Nº do Processo</label><input ng-model=detalharSuspensaoCtrl.suspensao.numeroProcesso name=numeroProcesso md-maxlength=17 required maxlength=17 ng-disabled=true></md-input-container></div><div layout=row><md-input-container class=md-block flex><label>Descricao</label><textarea ng-model=detalharSuspensaoCtrl.suspensao.descricao required ng-disabled=true></textarea></md-input-container></div><div layout=row><md-input-container class=md-block flex><label>Motivação</label><input ng-model=detalharSuspensaoCtrl.suspensao.motivacao.descricao required name=motivacao ng-disabled=true></md-input-container><md-input-container class=md-block flex><md-datepicker ng-mask=39/19/9999 restrict=reject ng-model=detalharSuspensaoCtrl.suspensao.vigencia placeholder=\"Início da Vigência\" required name=vigencia ng-disabled=true></md-datepicker></md-input-container></div><div layout=column style=\"margin-bottom: 20px\"><span>Rips:</span> <span ng-if=\"detalharSuspensaoCtrl.ripsAtivos.length === 0\">Nenhum RIP com suspensão ativa</span><md-chips ng-model=detalharSuspensaoCtrl.ripsAtivos readonly md-removable=true delete-hint=Cancelar md-on-remove=\"detalharSuspensaoCtrl.cancelarSuspensao($event, $chip)\" ng-if=\"detalharSuspensaoCtrl.ripsAtivos.length > 0\"><md-chip-template>{{$chip.rip}}</md-chip-template></md-chips></div><div layout=column style=\"margin-bottom: 20px\"><span>Restrições:</span><md-chips class=md-chips ng-model=detalharSuspensaoCtrl.suspensao.restricoes readonly md-removable=false><md-chip-template>{{$chip.descricao}}</md-chip-template></md-chips></div><div layout=column style=\"margin-bottom: 20px\" ng-show=\"detalharSuspensaoCtrl.ripsCancelados.length > 0\"><span>Rips Cancelados:</span><div layout=row layout-align=\"center start\"><div flex=90><md-table-container><table md-table md-progress=promise><thead md-head md-order=detalharSuspensaoCtrl.ordenacao><tr md-row><th md-column md-order-by=rip>RIP</th><th md-column md-order-by=cancelamentoSuspensaoImovel.motivo.descricao>Motivo</th><th md-column md-order-by=cancelamentoSuspensaoImovel.dataCancelamento>Data de Cancelamento</th><th md-column md-order-by=cancelamentoSuspensaoImovel.dataFimVigencia>Fim da Vigência</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in detalharSuspensaoCtrl.ripsCancelados | orderBy: detalharSuspensaoCtrl.ordenacao | limitTo: detalharSuspensaoCtrl.paginacao.limit: (detalharSuspensaoCtrl.paginacao.page - 1) * detalharSuspensaoCtrl.paginacao.limit\"><td md-cell>{{ item.rip }}</td><td md-cell>{{ item.cancelamentoSuspensaoImovel.motivo.descricao }}</td><td md-cell>{{ item.cancelamentoSuspensaoImovel.dataCancelamento | date:\'dd/MM/yyyy\' }}</td><td md-cell>{{ item.cancelamentoSuspensaoImovel.dataFimVigencia | date:\'dd/MM/yyyy\' }}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"detalharSuspensaoCtrl.detalharCancelamento($event, item)\" class=\"md-fab md-raised md-mini\" aria-label=\"Detalhar Cancelamento de Suspensão\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Detalhar Cancelamento de Suspensão</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=detalharSuspensaoCtrl.paginacao.limit md-limit-options=detalharSuspensaoCtrl.paginacao.limitOptions md-page=detalharSuspensaoCtrl.paginacao.page md-total=\"{{ detalharSuspensaoCtrl.paginacao.totalElements }}\" md-page-select ng-disabled=\"detalharSuspensaoCtrl.paginacao.totalElements < 6\"></md-table-pagination></div></div></div></ng-form></div>");
$templateCache.put("scripts-cadastro-imovel/pages/suspensao/view/dialog-cancelar-suspencao-imovel.html","<md-dialog aria-label=\"Cancelar Suspensão\" flex=80><ng-form name=cancelarSuspensaoCtrl.forms.cancelarSuspensaoImovelForm><md-toolbar><div class=md-toolbar-tools><h2>Cancelar Suspensão</h2><span flex></span><md-button class=md-icon-button ng-click=cancelarSuspensaoCtrl.fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div layout=column class=md-dialog-content><div layout=row><md-input-container class=md-block ng-if=!cancelarSuspensaoCtrl.isDetail flex><label>Motivo</label><md-select campo-ajuda=INC_FORM_CANCEL_SUSPENSAO_MOTIVO ng-model=cancelarSuspensaoCtrl.cancelamentoSuspensao.motivo ng-model-options=\"{trackBy: \'$value.id\'}\" name=motivo required><md-option campo-ajuda={{motivo.codigoSistemico}} ng-repeat=\"motivo in cancelarSuspensaoCtrl.motivosSuspensao | orderByBr:\'descricao\'\" ng-value=motivo>{{motivo.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block ng-if=cancelarSuspensaoCtrl.isDetail flex><label>Motivo</label><input campo-ajuda=INC_FORM_CANCEL_SUSPENSAO_MOTIVO ng-model=cancelarSuspensaoCtrl.cancelamentoSuspensao.motivo.descricao ng-disabled=true name=motivo required></md-input-container><md-input-container class=md-block flex><label>Nº do Processo</label><input campo-ajuda=INC_FORM_CANCEL_SUSPENSAO_N_PROCESSO ng-model=cancelarSuspensaoCtrl.cancelamentoSuspensao.numeroProcesso ng-disabled=cancelarSuspensaoCtrl.isDetail name=numeroProcesso required></md-input-container><md-input-container class=md-block flex><label>Nº da Folha/Documento</label><input campo-ajuda=INC_FORM_CANCEL_SUSPENSAO_N_FOLHA_DOC ng-model=cancelarSuspensaoCtrl.cancelamentoSuspensao.numeroFolha ng-disabled=cancelarSuspensaoCtrl.isDetail name=numeroFolha required></md-input-container><md-input-container class=md-block flex><label>Fim da Vigência</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_CANCEL_SUSPENSAO_FIM_VIGENCIA ng-model=cancelarSuspensaoCtrl.cancelamentoSuspensao.dataFimVigencia name=fimVigencia required ng-disabled=cancelarSuspensaoCtrl.isDetail md-min-date=cancelarSuspensaoCtrl.amanha inc-ajusta-tamanho></md-datepicker></md-input-container></div><div layout=row><md-input-container class=md-block flex><label>Observação</label><textarea campo-ajuda=INC_FORM_CANCEL_SUSPENSAO_OBS rows=2 ng-model=cancelarSuspensaoCtrl.cancelamentoSuspensao.observacao name=observacao ng-disabled=cancelarSuspensaoCtrl.isDetail required></textarea></md-input-container></div></div></md-dialog-content><md-dialog-actions><div layout=row layout-align=\"end right\"><md-button class=\"md-raised md-primary\" ng-click=cancelarSuspensaoCtrl.fechar()>Fechar</md-button><md-button class=\"md-raised md-primary\" ng-click=cancelarSuspensaoCtrl.salvar() ng-if=!cancelarSuspensaoCtrl.isDetail>Gravar</md-button></div></md-dialog-actions></ng-form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/suspensao/view/dialog-suspensao-cancelada.html","<md-dialog aria-label=\"CANCELAMENTO DA SUSPENSÃO\" flex=40><form name=detalharSuspensaoCancelada><md-toolbar><div class=md-toolbar-tools><h2>CANCELAMENTO DA SUSPENSÃO</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row flex-gt-xs><md-input-container class=md-block flex-gt-xs><label>Motivação*:</label><md-select required ng-model=suspenderImovel.motivacao ng-model-options=\"{trackBy: \'$value.id\'}\" name=motivo ng-disabled=true><md-option ng-repeat=\"item in lista.motivacoes\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs><label>Nº do Processo*:</label><input ng-model=suspenderImovel.numeroProcesso name=numeroProcesso md-maxlength=17 required maxlength=17 ng-disabled=true></md-input-container></div><div layout-gt-sm=row flex-gt-xs><md-input-container class=md-block flex-gt-xs><label>Nº da Folha/Documento*:</label><input ng-model=suspenderImovel.numeroFolha name=numeroFolha md-maxlength=17 required maxlength=17 ng-disabled=true></md-input-container><h5>Fim da Vigência*:</h5><md-datepicker ng-mask=39/19/9999 restrict=reject ng-model=suspenderImovel.fimVigencia md-placeholder=\"Fim da Vigência\" md-max-date=\"\" ng-required name=fimVigencia ng-disabled=true></md-datepicker></div><div layout-gt-sm=row flex-gt-xs><md-input-container class=md-block flex-gt-xs><label>Descricao*:</label><textarea ng-model=suspenderImovel.descricao ng-required md-maxlength=500 md-minlength=10></textarea></md-input-container></div><div layout=row layout-align=\"end right\"><md-button class=\"md-raised md-primary\" ng-click=fechar() ng-show=condicional>Fechar</md-button><md-button class=\"md-raised md-primary\" ng-click=salvarCancelamento(forms.cancelarImovelForm.$invalid) ng-show=condicional>Gravar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/terreno/view/arquivos-complementares.html","<div layout=row flex=100><div layout=column flex=100><div><upload-arquivos titulo=Documentos model=imovel.arquivosComplementaresLista></upload-arquivos></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/terreno/view/dados-acenssao-benfeitoria.html","<div layout=column><md-input-container><label>Terreno com Acessão/Benfeitoria?</label><md-select ng-model=imovel.terreno.benfeitoria aria-label=\"Terreno com Acessão/Benfeitoria?\" placeholder=\"Selecione uma opção\" ng-required=imovelCtrl.showAcenssaoBenfeitoria><md-option value=\"Não informado\">Não informado</md-option><md-option value=Sim>Sim</md-option><md-option value=Não>Não</md-option></md-select></md-input-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/terreno/view/dados-acessao-benfeitoria.html","<md-input-container ng-class=\"forms.terrenoForm.terrenoAcessaoBenfeitoria.$error.required && submittedTerreno ? \'md-input-invalid\': null\"><label>Terreno com Acessão/Benfeitoria?</label><md-select class=secaoBenfeitoria ng-model=imovel.terreno.benfeitoria aria-label=\"Terreno com Acenssão/Benfeitoria?\" placeholder=\"Selecione uma opção\" ng-required=imovelCtrl.showAcenssaoBenfeitoria name=terrenoAcessaoBenfeitoria><md-option value=Sim>Sim</md-option><md-option value=Não>Não</md-option></md-select></md-input-container>");
$templateCache.put("scripts-cadastro-imovel/pages/terreno/view/dados-descricao-cadastral.html","<div layout=column><div layout-gt-xs=row style=\"margin-bottom: 10px\"><md-radio-group class=secaoDescricaoCadastral ng-model=imovel.terreno.tipoDesenho style=\"float: left; margin-top: 10px\"><md-radio-button id=radio1 value=shapeFile ng-click=terrenoCtrl.alterarParaArquivoShapeFile($event) style=float:left>Arquivo(Shapefiles)</md-radio-button><md-radio-button id=radio2 value=vetorizacao ng-disabled=terrenoCtrl.arquivoCartorial.arquivo ng-click=terrenoCtrl.alterarParaVetorizacaoEmTela($event); style=\"float:left; margin-top: 0; margin-left: 15px !important\">Vetorização em Tela</md-radio-button><label style=\"margin-left: 15px; margin-top: 12px; color:red\" ng-show=\"imovel.terreno.tipoDesenho === \'vetorizacao\'\">Essa vetorização possui baixa qualidade de precisão.</label></md-radio-group></div><section layout=row layout-sm=column layout-align=\"right center\" layout-wrap><md-button class=\"md-raised md-primary\" ng-click=\"terrenoCtrl.arquivo.arquivo ? terrenoCtrl.showShapeFileCadastralJaEnviado($event) : terrenoCtrl.showUploadArquivoTerrenoDialog($event)\" ng-disabled=\"!(imovel.terreno.tipoDesenho === \'shapeFile\')\">Escolher Arquivo</md-button>{{ terrenoCtrl.arquivo.nome }}<ng-md-icon ng-hide=!terrenoCtrl.arquivo.nome icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=terrenoCtrl.excluirArquivoTerrenoCadastral($event)></ng-md-icon></section><md-input-container ng-class=\"forms.terrenoForm.memorialDescritivoCadastral.$error.required && submittedTerreno ? \'md-input-invalid\': null\" class=md-block flex-gt-xs><label>Memorial Descritivo</label><textarea class=secaoDescricaoCadastral ng-model=imovel.terreno.memorialDescritivoCadastral ng-required=\"imovel.terreno.geometriaTerreno && imovel.incorporacao.numeroMatricula === undefined\" md-maxlength=2000 name=memorialDescritivoCadastral></textarea></md-input-container><div layout=row layout-xs=column ng-show=imovel.terreno.geometriaTerreno><md-input-container class=md-block flex=25><label style=\"text-align: center\">Unidade de Medida da Área:</label></md-input-container><md-input-container class=md-block flex><md-radio-group name=unidadeMedidaCadastral style=\"float: left !important\" layout=row layout-xs=column ng-model=imovel.terreno.unidadeMedidaCadastral ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral()><md-radio-button id=radioUnidadeMedidaM value=m2>m²</md-radio-button><md-radio-button id=radioUnidadeMedidaHa value=ha>ha</md-radio-button></md-radio-group></md-input-container></div><div layout-gt-xs=row style=\"height: 50px\" ng-show=\"imovel.terreno.geometriaTerreno || imovel.terreno.areaTotalCadastral > 0\"><md-input-container ng-class=\"forms.terrenoForm.areaTotalCadastral.$error.required && submittedTerreno?\'md-input-invalid\':null\" class=md-block flex-gt-xs style=\"margin-bottom: 0px\"><label>Área Total</label><input type=text ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral(); class=secaoDescricaoCadastral ng-model=imovel.terreno.areaTotalCadastral ng-required=imovel.terreno.geometriaTerreno ng-blur=\"terrenoCtrl.validaFracaoIdealImovelCadastral(); terrenoCtrl.verificarAreaTotalValidaCadastral();\" ng-disabled=\"imovel.ripsFusao.length > 1\" ui-number-mask=2 name=areaTotalCadastral></md-input-container><md-input-container ng-class=\"forms.terrenoForm.areaUniaoCadastral.$error.required && submittedTerreno ? \'md-input-invalid\': null\" class=md-block flex-gt-xs ng-show=imovel.proprietario.isIntegralmente style=\"margin-bottom: 0px\"><label>Área da União</label><input type=text ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral(); class=secaoDescricaoCadastral ng-model=imovel.terreno.areaUniaoCadastral ng-required=\"imovel.terreno.geometriaTerreno && imovel.proprietario.isIntegralmente\" ng-blur=terrenoCtrl.validaFracaoIdealImovelCadastral(); ui-number-mask=2 name=areaUniaoCadastral></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block ng-model=text flex-gt-xs ng-show=\"imovel.terreno.unidadeMedidaCadastral == \'ha\' &&\n								imovel.terreno.areaTotalCadastral != undefined\" style=\"margin-top: 0px\">{{imovel.terreno.areaTotalCadastral/10000|number:2|numeroBr}} m²</md-input-container></div><div layout=row layout-xs=column ng-show=\"imovel.terreno.unidadeMedidaCadastral == \'ha\' && imovel.terreno.areaTotalCadastral != undefined\"><md-input-container style=\"margin-top: 0px\" class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerreno &&\n							imovel.proprietario.isIntegralmente &&\n							imovel.terreno.unidadeMedidaCadastral == \'ha\' &&\n							imovel.terreno.areaUniaoCadastral != undefined\">{{imovel.terreno.areaUniaoCadastral*10000|number:2|numeroBr}} m²</md-input-container></div><div layout-gt-xs=row><md-input-container ng-class=\"forms.terrenoForm.fracaoIdealImovelCadastral.$error.required && submittedTerreno?\'md-input-invalid\':null\" class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4\" style=\"margin-bottom: 0px\"><label>Fração Ideal do Imóvel</label><input type=text name=fracaoIdealImovelCadastral id=fracaoIdealImovelCadastral ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral(); class=secaoDescricaoCadastral ng-model=\"imovel.unidadeAutonoma.fracaoIdealCadastral \" ng-required=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4\" ng-blur=terrenoCtrl.validaFracaoIdealImovelCadastral(); ui-number-mask name=fracaoIdealImovelCadastral></md-input-container><md-input-container class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4 && imovel.proprietario.isIntegralmente\"><label style=\"margin-left: 90px !important\">Fração Ideal da União(m²)</label></md-input-container><md-input-container class=md-block flex-xs ng-show=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4 && imovel.proprietario.isIntegralmente\" style=\"margin-right: 300px !important\"><label>{{imovel.terreno.fracaoIdealUniaoCadastral|number:2|numeroBr}}</label></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs ng-show=\"\n							imovel.terreno.geometriaTerreno &&\n							imovel.tipoImovel.id === 4 &&\n							imovel.terreno.unidadeMedidaCadastral == \'ha\' &&\n							imovel.unidadeAutonoma.fracaoIdealCadastral  != undefined\" style=\"margin-top: 0px\">{{imovel.unidadeAutonoma.fracaoIdealCadastral *10000|number:2|numeroBr}} m²</md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/terreno/view/dados-descricao-cartorial.html","<div layout=column><div layout-gt-xs=row style=\"margin-bottom: 10px\"><md-radio-group class=secaoDescricaoCartorial ng-model=terrenoCtrl.terreno.tipoDesenhoCartorial style=\"float: left; margin-top: 10px\"><md-radio-button id=radio1 ng-value=222 ng-click=terrenoCtrl.alterarParaArquivoShapeFileCartorial($event) style=float:left>Arquivo (Shapefile)</md-radio-button><md-radio-button id=radio2 ng-value=333 ng-click=\"terrenoCtrl.alterarParaDescricaoNaoGeorreferenciada($event); imovelCtrl.limparMemorial()\" style=\"float:left; margin-top: 0; margin-left: 15px !important\">Descrição Não Georreferenciada</md-radio-button></md-radio-group></div><section layout=row layout-sm=column layout-align=\"right center\" layout-wrap><md-button id=btnEscolherArquivo class=\"md-raised md-primary\" ng-disabled=!terrenoCtrl.habilitarUploadArquivoTerrenoCartorial ng-click=\"terrenoCtrl.arquivoCartorial.arquivo ? terrenoCtrl.showShapeFileCartorialJaEnviado($event) : terrenoCtrl.showUploadArquivoTerrenoDialogCartorial($event)\">Escolher Arquivo</md-button>{{ terrenoCtrl.arquivoCartorial.nome }}<ng-md-icon ng-hide=!terrenoCtrl.arquivoCartorial.nome icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=terrenoCtrl.excluirArquivoTerrenoCartorial($event)></ng-md-icon></section><md-checkbox class=secaoDescricaoCartorial ng-model=imovel.terreno.isCertificaoIncra ng-show=imovelCtrl.showCertificadoIncra>Levantamento cadastral certificado pelo Incra</md-checkbox><md-input-container class=md-block flex-gt-xs ng-class=\"forms.terrenoForm.memorialDescritivoCartorial.$error.required && submittedTerreno?\'md-input-invalid md-input-focused\':null\"><label>Memorial Descritivo</label><textarea class=secaoDescricaoCartorial ng-model=imovel.terreno.memorialDescritivoCartorial ng-required=\"imovel.terreno.geometriaTerrenoCartorial && imovel.tipoImovel.id !== 5 && imovel.tipoImovel.id !== 4\" md-maxlength=2000 name=memorialDescritivoCartorial></textarea></md-input-container><div layout=row layout-xs=column ng-show=imovel.terreno.geometriaTerrenoCartorial><md-input-container class=md-block flex=25><label style=\"text-align: center\">Unidade de Medida da Área:</label></md-input-container><md-input-container class=md-block flex><md-radio-group name=unidadeMedidaCartorial style=\"float: left !important\" layout=row layout-xs=column ng-model=imovel.terreno.unidadeMedidaCartorial ng-change=terrenoCtrl.calcularFracaoIdealUniaoCartorial()><md-radio-button id=radioUnidadeMedidaM value=m2>m²</md-radio-button><md-radio-button id=radioUnidadeMedidaHa value=ha>ha</md-radio-button></md-radio-group></md-input-container></div><div layout-gt-xs=row style=\"height: 50px\"><md-input-container class=md-block flex-gt-xs ng-show=imovel.terreno.geometriaTerrenoCartorial style=\"margin-bottom: 0px\" ng-class=\"forms.terrenoForm.areaTotalCartorial.$error.required && submittedTerreno?\'md-input-invalid md-input-focused\':null\"><label>Área Total</label><input type=text class=secaoDescricaoCartorial ng-model=imovel.terreno.areaTotalCartorial ng-required=imovel.terreno.geometriaTerrenoCartorial ui-number-mask name=areaTotalCartorial ng-disabled=\"imovel.ripsFusao.length > 1\" ng-blur=terrenoCtrl.verificarAreaTotalValidaCartorial();></md-input-container><md-input-container class=md-block flex-gt-xs ng-show=imovel.proprietario.isIntegralmente style=\"margin-bottom: 0px\"><label>Área da União</label><input type=text class=secaoDescricaoCartorial ng-model=imovel.terreno.areaUniaoCartorial ng-required=\"imovel.terreno.geometriaTerrenoCartorial && imovel.proprietario.isIntegralmente\" ui-number-mask name=areaUniaoCartorial ng-change=terrenoCtrl.validaAreaUniaoCartorial();> <span class=form-error ng-show=\"forms.terrenoForm.areaUniaoCartorial.$error.required && submittedTerreno\">Campo de preenchimento obrigatório</span></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs ng-show=\"imovel.terreno.unidadeMedidaCartorial == \'ha\' &&\n              imovel.terreno.areaTotalCartorial != undefined\" style=\"margin-top: 0px\">{{imovel.terreno.areaTotalCartorial*10000|number:2|numeroBr}} m²</md-input-container><md-input-container class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerrenoCartorial &&\n              imovel.proprietario.isIntegralmente &&\n              imovel.terreno.unidadeMedidaCartorial == \'ha\' &&\n              imovel.terreno.areaUniaoCartorial != undefined\" style=\"margin-top: 0px\">{{imovel.terreno.areaUniaoCartorial*10000|number:2|numeroBr}} m²</md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/terreno/view/dialog-upload-arquivo-cartorial.html","<md-dialog aria-label=\"Upload do Arquivo Shapefile\" flex=60 ng-cloak id=dialogBenfeitoria><ng-form name=forms.uploadArquivoShapefile ng-submit=confirmar()><md-toolbar><div class=md-toolbar-tools><h2>Upload do Arquivo Shapefile</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><input type=file ngf-select ng-model=arquivoCartorial.zipfile name=file accept=.zip ngf-max-size=10MB ngf-model-invalid=errorFile> <span class=form-error ng-show=\"arquivoCartorial.zipfile === undefined && submittedArquivoTerreno\">Campo de preenchimento obrigatório</span></div><div layout-gt-sm=row layout-margin><md-datepicker ng-mask=39/19/9999 restrict=reject ng-model=arquivoCartorial.dataPlanta md-placeholder=\"Selecione a data\" required md-max-date=hoje name=dataPlanta></md-datepicker><span class=form-error ng-show=\"arquivoCartorial.dataPlanta === undefined && submittedArquivoTerreno\">Campo de preenchimento obrigatório</span><md-input-container ng-class=\"arquivoCartorial.escala === undefined && submittedArquivoTerreno ? \'md-input-invalid\': null\" class=md-block flex-gt-sm><label>Escala de Vetorização</label><input ng-model=arquivoCartorial.escala type=text name=escala ui-mask=1:9?9?9?9?9?9?9?9?9?9></md-input-container></div><div layout-gt-sm=row layout-margin><md-input-container ng-class=\"arquivoCartorial.tipoFormaLevantamento === undefined && submittedArquivoTerreno ? \'md-input-invalid\':null\" class=md-block flex-gt-sm><md-select name=formaLevantamento ng-model=arquivoCartorial.tipoFormaLevantamento ng-model-options=\"{trackBy: \'$value.id\'}\" placeholder=\"Forma da Levantamento\" ng-required=true><md-option ng-value=opt ng-repeat=\"opt in formaLevantamento\">{{ opt.descricao }}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-sm ng-hide=\"arquivoCartorial.tipoFormaLevantamento.id !== 1 && arquivoCartorial.tipoFormaLevantamento.id !== 2\" ng-class=\"arquivoCartorial.resolucaoEspacial === undefined && submittedArquivoTerreno ? \'md-input-invalid\':null\"><label>Resolução Espacial(m)</label><input ng-model=arquivoCartorial.resolucaoEspacial ui-number-mask ng-required=\"arquivoCartorial.tipoFormaLevantamento.id === 1 || arquivoCartorial.tipoFormaLevantamento.id === 2\" name=resolucaoEspacial></md-input-container><md-input-container class=md-block flex-gt-sm ng-hide=\"arquivoCartorial.tipoFormaLevantamento.id !== 3\" ng-class=\"arquivoCartorial.tipoEquipamento === undefined && submittedArquivoTerreno ? \'md-input-invalid\':null\"><md-select name=tipoEquipamento ng-model=arquivoCartorial.tipoEquipamento ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"arquivoCartorial.tipoFormaLevantamento.id === 3\" placeholder=\"Tipo de Equipamento Utilizado\"><md-option ng-value=opt ng-repeat=\"opt in tipoEquipamento| orderByBr:\'descricao\'\">{{ opt.descricao }}</md-option></md-select></md-input-container></div></div></md-dialog-content><md-dialog-actions layout=row layout-align=\"end right\"><md-button ng-click=fechar() class=\"md-raised md-primary\">Voltar</md-button><md-button ng-click=confirmar() style=\"margin-right: 20px\" class=\"md-raised md-primary\">Confirmar</md-button></md-dialog-actions></ng-form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/terreno/view/dialog-upload-arquivo.html","<md-dialog aria-label=\"Upload do Arquivo Shapefile\" flex=60 ng-cloak><ng-form name=forms.uploadArquivoShapefile ng-submit=uploadArqTerrenoCtrl.confirmar()><md-toolbar><div class=md-toolbar-tools><h2>Upload do Arquivo Shapefile</h2><span flex></span><md-button class=md-icon-button ng-click=uploadArqTerrenoCtrl.fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><input type=file ngf-select ng-model=uploadArqTerrenoCtrl.arquivo.zipfile name=file accept=.zip ngf-max-size=10MB ngf-model-invalid=errorFile> <span class=form-error ng-show=\"uploadArqTerrenoCtrl.arquivo.zipfile == undefined && submittedArquivoCadastral\">Campo de preenchimento obrigatório</span></div><div layout-gt-sm=row layout-margin><md-datepicker ng-mask=39/19/9999 restrict=reject ng-model=uploadArqTerrenoCtrl.arquivo.dataPlanta md-placeholder=\"Selecione a data\" required md-max-date=uploadArqTerrenoCtrl.hoje name=dataPlanta></md-datepicker><span class=form-error ng-show=\"!uploadArqTerrenoCtrl.arquivo.dataPlanta && submittedArquivoCadastral\">Campo de preenchimento obrigatório</span><md-input-container class=md-block flex-gt-sm ng-class=\"uploadArqTerrenoCtrl.arquivo.escala == undefined && sumbittedArquivoCadastral ? \'md-input-invalid md-input-focused\':null\"><label>Escala de Vetorização</label><input ng-model=uploadArqTerrenoCtrl.arquivo.escala type=text name=escala ui-mask=1:9?9?9?9?9?9?9?9?9?9></md-input-container></div><div layout-gt-sm=row layout-margin><md-input-container ng-class=\"!uploadArqTerrenoCtrl.arquivo.tipoFormaLevantamento && submittedArquivoCadastral ? \'md-input-invalid\':null\" class=md-block flex-gt-sm><md-select name=formaLevantamento ng-model=uploadArqTerrenoCtrl.arquivo.tipoFormaLevantamento ng-model-options=\"{trackBy: \'$value.id\'}\" placeholder=\"Forma de Levantamento\"><md-option ng-value=opt ng-repeat=\"opt in uploadArqTerrenoCtrl.formaLevantamento| orderByBr:\'descricao\'\">{{ opt.descricao }}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-sm ng-class=\"arquivo.resolucaoEspacial == undefined && submittedArquivoCadastral ? \'md-input-invalid\':null\" ng-hide=\"uploadArqTerrenoCtrl.arquivo.tipoFormaLevantamento.id !== 1 && uploadArqTerrenoCtrl.arquivo.tipoFormaLevantamento.id !== 2\"><label>Resolução Espacial(m)</label><input ng-model=uploadArqTerrenoCtrl.arquivo.resolucaoEspacial ui-number-mask ng-required=\"uploadArqTerrenoCtrl.arquivo.tipoFormaLevantamento.id == 1 || uploadArqTerrenoCtrl.arquivo.tipoFormaLevantamento.id == 2\" name=resolucaoEspacial></md-input-container><md-input-container ng-class=\"uploadArqTerrenoCtrl.arquivo.tipoEquipamento == undefined && submittedArquivoCadastral ? \'md-input-invalid\':null\" class=md-block flex-gt-sm ng-hide=\"uploadArqTerrenoCtrl.arquivo.tipoFormaLevantamento.id != 3\"><md-select name=tipoEquipamento ng-model=uploadArqTerrenoCtrl.arquivo.tipoEquipamento ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"uploadArqTerrenoCtrl.arquivo.tipoFormaLevantamento.id == 3\" placeholder=\"Tipo de Equipamento Utilizado\"><md-option ng-value=opt ng-repeat=\"opt in uploadArqTerrenoCtrl.tipoEquipamento| orderByBr:\'descricao\'\">{{ opt.descricao }}</md-option></md-select></md-input-container></div></div></md-dialog-content><md-dialog-actions layout=row layout-align=\"end right\"><md-button ng-click=uploadArqTerrenoCtrl.fechar()>Voltar</md-button><md-button ng-click=uploadArqTerrenoCtrl.confirmar() style=\"margin-right: 20px\">Confirmar</md-button></md-dialog-actions></ng-form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/terreno/view/inserir-terreno.tmpl.html","<div layout=row><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 showtestada=false tabindex=1 edificacoes=imovelCtrl.edificacoesUnidadeAutonoma imovel=imovel action=add habilitar-desenho=\"imovel.terreno.tipoGeometriaCadastral == \'VETORIZACAO\'\"></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout-xs=column flex=70 ng-controller=\"TerrenoController as terrenoCtrl\"><md-content flex><ng-form name=forms.terrenoForm enctype=multipart/form-data><v-accordion multiple class=vAccordion--default style=\"margin-top: 20px\"><v-pane ng-show=\"imovel.incorporacao.matriculas.length > 0\"><v-pane-header ng-click=\"imovelCtrl.validaSecao($event, forms.terrenoForm, 1, \'.secaoDescricaoCartorial\')\">Descrição Cartorial</v-pane-header><v-pane-content><!--<div ng-include=\"\'scripts-cadastro-imovel/pages/terreno/view/dados-descricao-cartorial.html\'\"></div>--><descricao-terreno-cartorial imovel=imovel tipos-geometria=imovelCtrl.tiposDesenhoGeometriaTerreno action=\"imovel.terreno.id != null ? \'detail\' : \'\' \"></descricao-terreno-cartorial></v-pane-content></v-pane><v-pane id=descricaoCadastral><v-pane-header ng-click=\"imovelCtrl.validaSecao($event, forms.terrenoForm, 1, \'.secaoDescricaoCadastral\')\">Descrição Cadastral</v-pane-header><v-pane-content id=descricaoCadastralContent><!--<div ng-include=\"\'scripts-cadastro-imovel/pages/terreno/view/dados-descricao-cadastral.html\'\"></div>--><descricao-terreno-cadastral imovel=imovel tipos-geometria=imovelCtrl.tiposDesenhoGeometriaTerreno action=\"imovel.terreno.id != null ? \'detail\' : \'\' \"></descricao-terreno-cadastral></v-pane-content></v-pane><v-pane><v-pane-header ng-click=\"imovelCtrl.validaSecao($event, forms.terrenoForm, 1, \'.secaoRestricoesUsoOcupacao\')\">Restrições de Uso/Ocupação</v-pane-header><v-pane-content><restricoes-uso-ocupacao terreno=imovel.terreno id-tipo-imovel=imovel.tipoImovel.id is-detail=\"imovel.terreno.id != null ? true : false \"></restricoes-uso-ocupacao></v-pane-content></v-pane><v-pane><v-pane-header ng-click=\"imovelCtrl.validaSecao($event, forms.terrenoForm, 1, \'.secaoArquivosComplementares\')\">Arquivos Complementares</v-pane-header><v-pane-content><div ng-include=\"\'scripts-cadastro-imovel/pages/terreno/view/arquivos-complementares.html\'\"></div></v-pane-content></v-pane></v-accordion></ng-form><div layout=row layout-align=\"end center\"><md-button md-no-ink class=\"md-raised md-secondary\" ng-click=selectTab(0)>Retornar</md-button><md-button class=\"md-raised md-secondary\" ng-click=imovelCtrl.limparImovel(1)>Limpar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=imovelCtrl.submitFormularioTerreno()>Avançar</md-button></div></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/unidade-autonoma/view/cadastro-geometria.html","<div layout=column><div layout-gt-xs=row style=\"margin-bottom: 10px\"><md-radio-group class=secaoDadosEdificacao ng-model=imovel.unidadeAutonoma.tipoArquivo style=\"float: left; margin-top: 10px\"><md-radio-button id=radio1 ng-value=1 style=float:left>Arquivo (Shapefile)</md-radio-button><md-radio-button id=radio2 ng-value=2 style=\"float:left; margin-top: 0; margin-left: 15px !important\" checked>Vetorização em Tela</md-radio-button></md-radio-group></div><input class=secaoDadosEdificacao ng-model=imovelCtrl.unidadeAutonoma.file type=file style=\"margin-bottom: 25px\" ng-disabled=\"imovel.unidadeAutonoma.tipoArquivo === 2\"><div layout=row><md-input-container ng-class=\"forms.adicionarUnidadeAutonomaForm.tipoEdificacao.$error.required && submittedUnidadeAutonoma?\'md-input-invalid\':null\" flex><label>Tipo de edificação</label><md-select class=secaoDadosEdificacao ng-model=imovel.unidadeAutonoma.tipoEdificacao ng-model-options=\"{trackBy : $value.id}\" ng-required=!imovelCtrl.isGaragem name=tipoEdificacao><md-option ng-repeat=\"item in imovelCtrl.tiposEdificacao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex ng-show=!imovelCtrl.isGaragem ng-class=\"forms.adicionarUnidadeAutonomaForm.tipoEstrutura.$error.required && submittedUnidadeAutonoma?\'md-input-invalid\':null\"><label>Tipo de Estrutura</label><md-select class=secaoDadosEdificacao ng-model=imovel.unidadeAutonoma.tipoEstrutura ng-model-options=\"{trackBy : $value.id}\" ng-required=!imovelCtrl.isGaragem name=tipoEstrutura><md-option ng-repeat=\"item in imovelCtrl.tiposEstrutura\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row ng-hide=\"imovel.unidadeAutonoma.tipoEdificacao.id === 4\"><md-input-container><md-checkbox class=secaoDadosEdificacao ng-model=imovel.unidadeAutonoma.possuiElevador>Possui elevador</md-checkbox></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/unidade-autonoma/view/condicoes-acessibilidade-garagem.html","<div layout=column><div layout=row layout-xs=column><md-input-container>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</md-input-container></div><div layout=row layout-xs=column><md-radio-group name=acessosLivresBarreirasArquitetonicas class=secaoCondicoesAcessibilidadeGaragem ng-model=imovel.unidadeAutonoma.acessosLivresBarreirasArquitetonicas><md-radio-button id=radioAcessosLivresBarreirasArquitetonicasSim ng-value=true style=\"float: left; margin-left:20px !important\">Sim</md-radio-button><md-radio-button id=radioAcessosLivresBarreirasArquitetonicasNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0  !important\">Não</md-radio-button></md-radio-group>&nbsp;&nbsp;&nbsp;&nbsp;<md-radio-group name=semInfoAcessibilidade class=secaoCondicoesAcessibilidadeGaragem ng-model=imovel.unidadeAutonoma.semInfoAcessibilidade><md-radio-button id=radioSemInfoAcessibilidade ng-value=true style=\"float: left; margin-left: 20px margin-top:0px !important\">Sem Informações de acessibilidade</md-radio-button></md-radio-group></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/unidade-autonoma/view/condicoes-acessibilidade.html","<div layout=column><div layout=row layout-xs=column><md-input-container><md-checkbox class=secaoCondicoesAcessibilidade ng-model=imovel.unidadeAutonoma.semInfoAcessibilidade ng-click=imovelCtrl.desselecionaRadioButtons();>Sem Informações de acessibilidade</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container>Há vagas próximas dos acessos de circulação de pedestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.<span ng-show=!imovel.unidadeAutonoma.semInfoAcessibilidade>*</span></md-input-container></div><div layout=row layout-xs=column><md-radio-group name=vagasProximasAcessoCirculacao class=secaoCondicoesAcessibilidade ng-model=imovel.unidadeAutonoma.vagasProximasAcessoCirculacao ng-required=\"!imovel.unidadeAutonoma.semInfoAcessibilidade && !imovelCtrl.isGaragem\"><md-radio-button id=radioVagasProximasDeficientesSim ng-value=true style=\"float: left; margin-left: 20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioVagasProximasDeficientesNao ng-value=false style=\"float: left; margin-left: 20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.vagasProximasAcessoCirculacao.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row layout-xs=column><md-input-container>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.<span ng-show=!imovel.unidadeAutonoma.semInfoAcessibilidade>*</span></md-input-container></div><div layout=row layout-xs=column><md-radio-group name=acessosLivresBarreirasArquitetonicas class=secaoCondicoesAcessibilidade ng-model=imovel.unidadeAutonoma.acessosLivresBarreirasArquitetonicas ng-required=\"!imovel.unidadeAutonoma.semInfoAcessibilidade && !imovelCtrl.isGaragem\"><md-radio-button id=radioAcessosLivresBarreirasArquitetonicasSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioAcessosLivresBarreirasArquitetonicasNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0  !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.acessosLivresBarreirasArquitetonicas.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row layout-xs=column><md-input-container>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.<span ng-show=!imovel.unidadeAutonoma.semInfoAcessibilidade>*</span></md-input-container></div><div layout=row layout-xs=column><md-radio-group name=banheiroAcessivel class=secaoCondicoesAcessibilidade ng-model=imovel.unidadeAutonoma.banheiroAcessivel ng-required=\"!imovel.unidadeAutonoma.semInfoAcessibilidade && !imovelCtrl.isGaragem\"><md-radio-button id=radioBanheiroAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioBanheiroAcessivelNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.banheiroAcessivel.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row ng-show=imovel.unidadeAutonoma.possuiElevador><md-input-container>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.<span ng-show=!imovel.unidadeAutonoma.semInfoAcessibilidade>*</span></md-input-container></div><div layout=row ng-show=imovel ng-show=imovel.unidadeAutonoma.possuiElevador><md-radio-group name=possuiElevador class=secaoCondicoesAcessibilidade ng-model=imovel.unidadeAutonoma.elevadorAcessivel ng-show=imovel.unidadeAutonoma.possuiElevador ng-required=\"imovel.unidadeAutonoma.possuiElevador && !imovel.unidadeAutonoma.semInfoAcessibilidade && !imovelCtrl.isGaragem\"><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-show=imovel.unidadeAutonoma.possuiElevador ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.possuiElevador.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/unidade-autonoma/view/dados-garagem.html","<div layout=column><div layout=row><md-input-container ng-class=\"forms.adicionarUnidadeAutonomaForm.areaGaragem.$error.required && submittedUnidadeAutonoma?\'md-input-invalid md-input-focused\':null\" flex><label>Área da Garagem</label><input class=secaoDadosGaragem ng-model=imovel.unidadeAutonoma.areaGaragem ui-number-mask ng-required=imovelCtrl.isGaragem name=areaGaragem></md-input-container><md-input-container flex><label>Tipo de garagem</label><md-select class=secaoDadosGaragem ng-model=imovel.unidadeAutonoma.tipoGaragem ng-model-options=\"{trackBy : $value.id}\" ng-required=imovelCtrl.isGaragem name=tipoGaragem><md-option ng-value=1>Coberta</md-option><md-option ng-value=2>Descoberta</md-option></md-select><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.tipoGaragem.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></md-input-container></div><div layout=row><md-input-container class=md-block flex><label>Memorial Descritivo</label><textarea class=secaoDadosGaragem ng-model=imovel.unidadeAutonoma.memorialDescritivoGaragem md-maxlength=2000></textarea></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/unidade-autonoma/view/dados-unidade-autonoma.html","<div layout=column><div layout=row layout-xs=column><md-input-container ng-class=\"forms.adicionarUnidadeAutonomaForm.areaConstruida.$error.required && submittedUnidadeAutonoma?\'md-input-invalid\':null\"><label>Área construída</label><input class=secaoDadosUA ng-model=imovel.unidadeAutonoma.areaConstruida ui-number-mask ng-required=!imovelCtrl.isGaragem name=areaConstruida></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs><label>Memorial Descritivo</label><textarea class=secaoDadosUA ng-model=imovel.unidadeAutonoma.memorialDescritivo md-maxlength=2000></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block ng-class=\"forms.adicionarUnidadeAutonomaForm.numeroDormitorios.$error.required && submittedUnidadeAutonoma?\'md-input-invalid\':null\" flex ng-show=\"imovel.tipoUnidadeAutonoma.id === 1\"><label>Número de dormitórios</label><input class=secaoDadosUA ng-model=imovel.unidadeAutonoma.numeroDormitorios type=number ng-required=\"imovel.tipoUnidadeAutonoma.id === 1\" name=numeroDormitorios></md-input-container><md-input-container ng-class=\"forms.adicionarUnidadeAutonomaForm.padraoAcabamento.$error.required && submittedUnidadeAutonoma?\'md-input-invalid\':null\" flex><label>Padrão de acabamento</label><md-select class=secaoDadosUA ng-model=imovel.unidadeAutonoma.padraoAcabamento ng-model-options=\"{trackBy : $value.id}\" ng-required=!imovelCtrl.isGaragem name=padraoAcabamento><md-option ng-repeat=\"item in imovelCtrl.tiposAcabamento\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex ng-class=\"forms.adicionarUnidadeAutonomaForm.estadoConservacao.$error.required && submittedUnidadeAutonoma?\'md-input-invalid\':null\"><label>Estado de conservação</label><md-select class=secaoDadosUA ng-model=imovel.unidadeAutonoma.estadoConservacao ng-model-options=\"{trackBy : $value.id}\" ng-required=!imovelCtrl.isGaragem name=estadoConservacao><md-option ng-repeat=\"item in imovelCtrl.estadosConservacao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container style=\"margin-right: 25px\"><md-checkbox class=secaoDadosUA ng-model=imovel.unidadeAutonoma.possuiGaragem>Possui garagem</md-checkbox></md-input-container><md-input-container ng-show=imovel.unidadeAutonoma.possuiGaragem><md-checkbox class=secaoDadosUA ng-model=imovel.unidadeAutonoma.garagemComMatricula>Garagem com matrícula</md-checkbox></md-input-container></div><div layout=row ng-show=imovel.unidadeAutonoma.possuiGaragem><md-input-container flex ng-class=\"forms.adicionarUnidadeAutonomaForm.tipoGaragemUA.$error.required && submittedUnidadeAutonoma?\'md-input-invalid\':null\"><label>Tipo de garagem</label><md-select class=secaoDadosUA ng-model=imovel.unidadeAutonoma.tipoGaragemUA ng-model-options=\"{trackBy : $value.id}\" ng-required=imovel.unidadeAutonoma.possuiGaragem name=tipoGaragemUA><md-option ng-value=1>Coberta</md-option><md-option ng-value=2>Descoberta</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.adicionarUnidadeAutonomaForm.numeroVagas.$error.required && submittedUnidadeAutonoma?\'md-input-invalid\':null\" class=md-block flex-gt-xs><label>Quantidade de vagas</label><input class=secaoDadosUA ng-model=imovel.unidadeAutonoma.numeroVagas type=number name=numeroVagas ng-required=imovel.unidadeAutonoma.possuiGaragem></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/unidade-autonoma/view/dialog-upload-arquivo-ua.html","<md-dialog aria-label=\"Upload do Arquivo Shapefile\" flex=60 ng-cloak><form name=uploadArquivoShapefile ng-submit=confirmar()><md-toolbar><div class=md-toolbar-tools><h2>Upload do Arquivo Shapefile</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><input type=file ngf-select ng-model=arquivo.arquivo name=file accept=.zip ngf-max-size=10MB ngf-model-invalid=errorFile> <span class=form-error ng-show=forms.unidadeAutonomaForm.file.$error.required>Campo de preenchimento obrigatório</span></div><div layout-gt-sm=row layout-margin><md-datepicker ng-mask=39/19/9999 restrict=reject ng-model=arquivo.dataPlanta md-placeholder=\"Selecione a data\" required name=dataPlanta></md-datepicker><span class=form-error ng-show=forms.terrenoForm.dataPlanta.$error.required>Campo de preenchimento obrigatório</span><md-input-container class=md-block flex-gt-sm><label>Escala de Vetorização</label><input ng-model=arquivo.escala type=text name=escala ui-mask=1:9?9?9?9?9?9?9?9?9?9> <span class=form-error ng-show=forms.terrenoForm.escala.$error.required>Campo de preenchimento obrigatório</span></md-input-container></div><div layout-gt-sm=row layout-margin><md-input-container class=md-block flex-gt-sm><md-select name=formaLevantamento ng-model=arquivo.tipoFormaLevantamento ng-model-options=\"{trackBy: \'$value.id\'}\" placeholder=\"Forma de Levantamento\"><md-option ng-value=opt ng-repeat=\"opt in uploadArqUACtrl.formaLevantamento| orderByBr:\'descricao\'\">{{ opt.descricao }}</md-option></md-select><span class=form-error ng-show=forms.terrenoForm.formaLevantamento.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-input-container class=md-block flex-gt-sm ng-hide=\"uploadArqUACtrl.arquivo.formaLevantamento.id !== 1 && uploadArqUACtrl.arquivo.formaLevantamento.id !== 2\"><label>Resolução Espacial(m)</label><input ng-model=arquivo.vl_resolucao_espacial ui-number-mask ng-required=\"formaLevantamento.id === 1 || formaLevantamento.id === 2\" name=resolucaoEspacial> <span class=form-error ng-show=forms.terrenoForm.resolucaoEspacial.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-input-container class=md-block flex-gt-sm ng-hide=\"uploadArqUACtrl.arquivo.formaLevantamento.id !== 3\"><md-select name=tipoEquipamento ng-model=arquivo.id_tipo_equipamento ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"formaLevantamento.id === 3\" placeholder=\"Tipo de Equipamento Utilizado\"><md-option ng-value=opt ng-repeat=\"opt in uploadArqUACtrl.tipoEquipamento| orderByBr:\'descricao\'\">{{ opt.descricao }}</md-option></md-select><span class=form-error ng-show=forms.terrenoForm.tipoEquipamento.$error.required>Campo de preenchimento obrigatório</span></md-input-container></div></div></md-dialog-content><md-dialog-actions layout=row layout-align=\"end right\"><md-button ng-click=fechar() class=\"md-raised md-primary\">Voltar</md-button><md-button type=submit style=\"margin-right: 20px\" class=\"md-raised md-primary\">Confirmar</md-button></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/unidade-autonoma/view/dialog-upload-planta-unidade.html","<md-dialog aria-label=\"Upload de Planta da Unidade Autônoma\" flex=60 ng-cloak><form name=uploadPlanta><md-toolbar><div class=md-toolbar-tools><h2>Upload de Planta da Unidade Autônoma</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><md-input-container class=md-block flex-gt-sm><label>Nome</label><input ng-model=plantaUnidade.nome type=text required name=nomePlantaUnidade> <span class=form-error ng-show=forms.adicionarUnidadeAutonomaForm.nomePlantaUnidade.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-datepicker ng-mask=39/19/9999 restrict=reject ng-model=plantaUnidade.dataEmissao md-placeholder=\"Data de emissão\" md-max-date=hoje required name=dataEmissaoPlantaUnidade></md-datepicker><span class=form-error ng-show=forms.adicionarUnidadeAutonomaForm.dataEmissaoPlantaUnidade.$error.required>Campo de preenchimento obrigatório</span></div><div layout-gt-sm=row layout-margin><input type=file ngf-select ng-model=plantaUnidade.planta accept=\".pdf, .png, .jpg\" ngf-max-size=10MB ngf-model-invalid=errorFile required name=plantaUnidade id=plantaUnidadeUpload> <span class=form-error ng-show=forms.adicionarUnidadeAutonomaForm.plantaUnidade.$error.required>Campo de preenchimento obrigatório</span><md-button layout-align=right class=\"md-primary md-raised\" ng-click=incluir()>Inserir</md-button></div><div layout=row ng-show=\"plantasUnidade.length > 0\"><md-content style=\"width: 100% !important\"><h4>Plantas da unidade autônoma</h4><table class=mui-table style=\"width:100% !important\"><thead><tr><th style=\"width: 200px !important\">Nome da certidão</th><th>Data da emissão</th><th>Nome do arquivo</th><th>Ação</th></tr></thead><tbody><tr ng-repeat=\"item in plantasUnidade\" ng-model-options=\"{trackBy: \'$value.id\'}\"><td>{{item.nome}}</td><td>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td>{{item.nomeArquivo}}</td><td><ng-md-icon icon=search style=fill:#788489 size=18 alt=\"Visualizar documento\" ng-click=visualizarPlanta(item.id)></ng-md-icon><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=removerPlanta($index)></ng-md-icon></td></tr></tbody></table></md-content></div><div layout-gt-sm=row layout-margin layout-align=\"end right\"><md-button layout-align=right class=\"md-primary md-raised\" ng-click=confirmar()>Confirmar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/unidade-autonoma/view/documentacao-unidade-autonoma.html","<div layout=column><div layout=row layout-xs=column><md-input-container><md-checkbox class=secaoDadosDocumentacao ng-model=imovelCtrl.imovel.unidadeAutonoma.possuiPlanoProtecaoIncendio aria-label=\"Plano de proteção contra incêndio\">Plano de proteção contra incêndio</md-checkbox></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacao\" ngf-select ng-model=imovelCtrl.imovel.unidadeAutonoma.planoProtecaoIncendio style=\"margin: -4px 0 0 15px\" ng-change=\"file2blob(\'planoProtecaoIncendioFile\', imovelCtrl.imovel.unidadeAutonoma.planoProtecaoIncendio)\" type=file ng-disabled=!imovelCtrl.imovel.unidadeAutonoma.possuiPlanoProtecaoIncendio accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-required=\"imovel.unidadeAutonoma.possuiPlanoProtecaoIncendio && imovelCtrl.imovel.unidadeAutonoma.planoProtecaoIncendio === undefined\" name=planoProtecaoIncendioFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.planoProtecaoIncendioFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"imovelCtrl.imovel.unidadeAutonoma.planoProtecaoIncendio != undefined\"><ng-md-icon ng-click=\"removerDocumento(\'planoProtecaoIncendioFile\')\" class=icon-delete icon=delete></ng-md-icon></md-input-container></div><div layout=row layout-xs=column><md-input-container><md-checkbox class=secaoDadosDocumentacao ng-model=imovelCtrl.imovel.unidadeAutonoma.possuiHabitese aria-label=Habite-se>Habite-se</md-checkbox></md-input-container><md-input-container ng-show=imovel.unidadeAutonoma.possuiHabitese style=\"margin: 12px 0 0 20px\"><label>Ano*:</label><input class=secaoDadosDocumentacao ng-model=imovelCtrl.imovel.unidadeAutonoma.anoHabitese type=number maxlength=4 name=ano mask-repeat=4 ng-required=imovel.unidadeAutonoma.possuiHabitese> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.ano.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacao\" ngf-select ng-model=imovelCtrl.imovel.unidadeAutonoma.habitese type=file style=\"margin: -4px 0 0 15px\" ng-change=\"file2blob(\'habiteseFile\', imovelCtrl.imovel.unidadeAutonoma.habitese)\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-disabled=!imovelCtrl.imovel.unidadeAutonoma.possuiHabitese ng-required=\"imovelCtrl.imovel.unidadeAutonoma.possuiHabitese && imovelCtrl.imovel.unidadeAutonoma.habitese === undefined\" name=habiteseFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.habiteseFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"imovelCtrl.imovel.unidadeAutonoma.habitese != undefined\"><ng-md-icon ng-click=\"removerDocumento(\'habiteseFile\')\" class=icon-delete icon=delete></ng-md-icon></md-input-container></div><div layout=row layout-xs=column><md-input-container><md-checkbox class=secaoDadosDocumentacao ng-model=imovelCtrl.imovel.unidadeAutonoma.possuiAlvaraConstrucao aria-label=\"Alvará de construção/reforma\">Alvará de construção/reforma</md-checkbox></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacao\" ngf-select ng-model=imovelCtrl.imovel.unidadeAutonoma.alvaraConstrucao ng-change=\"file2blob(\'alvaraConstrucaoFile\', imovelCtrl.imovel.unidadeAutonoma.alvaraConstrucao)\" type=file style=\"margin: -4px 0 0 15px\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-disabled=!imovelCtrl.imovel.unidadeAutonoma.possuiAlvaraConstrucao ng-required=\"imovelCtrl.imovel.unidadeAutonoma.possuiAlvaraConstrucao && imovelCtrl.imovel.unidadeAutonoma.alvaraConstrucao === undefined\" name=alvaraConstrucaoFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.alvaraConstrucaoFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"imovelCtrl.imovel.unidadeAutonoma.alvaraConstrucao != undefined\"><ng-md-icon ng-click=\"removerDocumento(\'alvaraConstrucaoFile\')\" class=icon-delete icon=delete></ng-md-icon></md-input-container></div><div layout=row layout-xs=column><md-input-container><md-checkbox class=secaoDadosDocumentacao ng-model=imovelCtrl.imovel.unidadeAutonoma.possuiLaudoPredial aria-label=\"Laudo de inspeção predial\">Laudo de inspeção predial</md-checkbox></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacao\" ngf-select ng-model=imovelCtrl.imovel.unidadeAutonoma.laudoPredial ng-change=\"file2blob(\'laudoPredialFile\', imovelCtrl.imovel.unidadeAutonoma.laudoPredial)\" type=file accept=.pdf,.img,.jpg,.jpeg style=\"margin: -4px 0 0 15px\" ngf-max-size=10MB ng-disabled=!imovelCtrl.imovel.unidadeAutonoma.possuiLaudoPredial ng-required=\"imovelCtrl.imovel.unidadeAutonoma.possuiLaudoPredial && imovelCtrl.imovel.unidadeAutonoma.laudoPredial === undefined\" name=laudoPredialFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.laudoPredialFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"imovelCtrl.imovel.unidadeAutonoma.laudoPredial != undefined\"><ng-md-icon ng-click=\"removerDocumento(\'laudoPredialFile\')\" class=icon-delete icon=delete></ng-md-icon></md-input-container></div><div layout-gt-xs=row><md-input-container><label>Outro:</label><textarea class=secaoDadosDocumentacao ng-model=imovelCtrl.imovel.unidadeAutonoma.outroDescricao>\n		</md-input-container>\n\n		<md-input-container>\n			<input class=\"md-raised secaoDadosDocumentacao\" style=\"margin: -4px 0 0 15px\" ngf-select ng-model=imovelCtrl.imovel.unidadeAutonoma.outro ng-change=\"file2blob(\'outroFile\', imovelCtrl.imovel.unidadeAutonoma.outro)\" type=file ng-disabled=\"imovelCtrl.imovel.unidadeAutonoma.outroDescricao === undefined\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-required=\"imovelCtrl.imovel.unidadeAutonoma.outroDescricao !== undefined && imovelCtrl.imovel.unidadeAutonoma.outro === undefined\" name=outroDescricaoFile>\n			<span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.outroDescricaoFile.$error.required && submittedUnidadeAutonoma\">\n				Campo de preenchimento obrigatório\n			</span>\n		</md-input-container>\n	</div>\n\n  <div ng-show=\"imovelCtrl.outrosDocumentos.length > 0\">\n    <md-table-container>\n      <table md-table>\n        <thead md-head>\n          <tr md-row>\n            <th md-column style=\"text-align: center\"><span>Nome</span></th>\n            <th md-column style=\"text-align: center\"><span>Excluir</span></th>\n          </tr>\n        </thead>\n        <tbody md-body>\n        <tr md-row md-select-id=id md-auto-select ng-repeat=\"outroDocumento in imovelCtrl.outrosDocumentos track by $index\">\n          <td md-cell style=\"text-align: center\">{{outroDocumento.nomeExtensao}}</td>\n          <td md-cell style=\"text-align: center\">\n            <md-input-container ng-show=\"outroDocumento.documento != undefined\">\n              <ng-md-icon ng-click=\"removerOutroDocumento($index, outroDocumento.nome)\" class=icon-delete icon=delete></ng-md-icon>\n            </md-input-container>\n          </td>\n        </tr>\n        </tbody>\n      </table>\n    </md-table-container>\n  </div>\n\n</div>");
$templateCache.put("scripts-cadastro-imovel/pages/unidade-autonoma/view/inserir-unidade-autonoma.tmpl.html","<div layout=row ng-show=imovelCtrl.showFormularioUnidadeAutonoma()><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=3 imovel=imovel action=add edificacoes=imovelCtrl.edificacoesUnidadeAutonoma habilitar-desenho=imovelCtrl.tipoVetorizacao()></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><album-fotos model=imovel.unidadeAutonoma.fotos></album-fotos><!--<div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos.html\'\"></div>--></fieldset></div></div><div layout=row flex=70 layout-xs=column><md-content flex><ng-form name=forms.adicionarUnidadeAutonomaForm><v-accordion multiple class=vAccordion--default><v-pane expanded ng-if=!imovelCtrl.isGaragem><v-pane-header ng-click=\"imovelCtrl.validaSecao($event, forms.adicionarUnidadeAutonomaForm, 3, \'.secaoDadosEdificacao\')\">Dados da Edificação</v-pane-header><v-pane-content><dados-edificacao-unidade-autonoma unidade-autonoma=imovelCtrl.imovel.unidadeAutonoma enderecamento=imovelCtrl.imovel.enderecamento forma-cadastro-edificacao=imovelCtrl.tipoCadastroEdificacaoUnidadeAutonoma show-tab-form-documento=imovelCtrl.mostraTabFormDocumento documento-backup=imovelCtrl.documentoBackup documento=vm.documento></dados-edificacao-unidade-autonoma></v-pane-content></v-pane><v-pane expanded ng-if=imovelCtrl.isGaragem><v-pane-header ng-click=\"imovelCtrl.validaSecao($event, forms.adicionarUnidadeAutonomaForm, 3, \'.secaoDadosGaragem\')\">Dados da Garagem</v-pane-header><v-pane-content><dados-garagem-unidade-autonoma unidade-autonoma=imovelCtrl.imovel.unidadeAutonoma></dados-garagem-unidade-autonoma></v-pane-content></v-pane><v-pane ng-if=!imovelCtrl.isGaragem><v-pane-header ng-click=\"imovelCtrl.validaSecao($event, forms.adicionarUnidadeAutonomaForm, 3, \'.secaoDadosUA\')\">Dados da Unidade Autônoma</v-pane-header><v-pane-content><dados-unidade-autonoma imovel=imovelCtrl.imovel tipo-imovel=imovelCtrl.imovel.tipoImovel unidade-autonoma=imovelCtrl.imovel.unidadeAutonoma></dados-unidade-autonoma></v-pane-content></v-pane></v-accordion></ng-form><div layout=row layout-align=\"end center\"><md-button md-no-ink class=\"md-raised md-secondary\" ng-click=imovelCtrl.tabs.previousTab();>Retornar</md-button><md-button class=\"md-raised md-secondary\" ng-click=imovelCtrl.limparUnidadeAutonoma()>Limpar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=imovelCtrl.submitFormularioUnidadeAutonoma()>Avançar</md-button></div></md-content></div></div><div layout=column ng-if=\"imovelCtrl.tipoCadastroEdificacaoUnidadeAutonoma === \'EXISTENTE\' && !imovelCtrl.showFormularioUnidadeAutonoma();\"><selecao-edificacao-unidade-autonoma edificacao=imovelCtrl.imovel.unidadeAutonoma.edificacao condicoes-acessibilidade=imovelCtrl.imovel.unidadeAutonoma.condicoesAcessibilidade terreno=imovelCtrl.imovel.terreno on-voltar=imovelCtrl.cancelarSelecaoEdificacaoUnidadeAutonoma() edificacoes=imovelCtrl.edificacoesUnidadeAutonoma></selecao-edificacao-unidade-autonoma></div>");
$templateCache.put("scripts-cadastro-imovel/pages/unidade-autonoma/view/planta-unidade.html","<div layout=column><div layout=row><md-button class=\"md-raised md-primary\" ng-click=\"imovelCtrl.resetarPlantaUnidade(); imovelCtrl.showUploadPlantaUnidadeDialog($event)\" aria-label=\"upload planta unidade\">Adicionar plantas da unidade</md-button></div><div layout=row ng-show=\"imovelCtrl.imovel.unidadeAutonoma.plantasUnidade.length > 0\"><md-content style=\"width: 100% !important\"><h4>Plantas da unidade autônoma</h4><table class=mui-table style=\"width:100% !important\"><thead><tr><th style=\"width: 200px !important\">Nome da certidão</th><th>Data da emissão</th><th>Nome do arquivo</th><th>Ação</th></tr></thead><tbody><tr ng-repeat=\"item in imovelCtrl.imovel.unidadeAutonoma.plantasUnidade\" ng-model-options=\"{trackBy: \'$value.id\'}\" style=\"text-align: center\"><td>{{item.nome}}</td><td>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td>{{item.nomeArquivo}}</td><td><ng-md-icon icon=search style=fill:#788489 size=18 alt=\"Visualizar documento\" ng-click=imovelCtrl.visualizarPlanta(item.id)></ng-md-icon><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=imovelCtrl.removerPlanta($index)></ng-md-icon></td></tr></tbody></table></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/album-fotos/template/album-fotos-detalhar-modal.html","<md-dialog aria-label=Fotos flex><md-toolbar><div class=md-toolbar-tools><h2>Fotos do imóvel</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><inc-carousel imagens=slides src=true></inc-carousel></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/app/directives/album-fotos/template/album-fotos.html","<div layout=column><inc-carousel imagens=ctrl.slides link-imagem=visualizaFotosModal(ev)></inc-carousel></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/ci-mapa/view/ci-mapa-homologar-template.html","<div class=div-mapa><div layout=column class=\"mapa-controles mapa-ferramentas\"><md-button class=\"md-icon-button mapa-botoes-icone\" ng-click=alterarVisibilidadeBalaoCamadas(); ng-if=\"tabIndex !== 4\"><ng-md-icon icon=layers style=\"fill: #fff\"><md-tooltip>Camadas</md-tooltip></ng-md-icon></md-button></div><div class=\"mapa-controles controles-bottom\" layout=row layout-align=\"end end\"><div ng-if=showMinimapa class=mapa-minimapa ng-click=alterarBingVisible()><ci-minimapa height={{heightMinimapa}} width={{widthMinimapa}}></ci-minimapa></div></div><div ng-show=listaLayers class=\"mapa-controles mapa-lista-camadas\" style=\"overflow-y: scroll; max-height: 350px\"><div class=mapa-lista-camadas-title ng-show=apresentaLabelOriginal()>Informação Original</div><div ng-repeat=\"item in layers.getArray() | filter: layerFilter\"><md-checkbox ng-checked=item.getVisible(); ng-click=\"alterarLayerVisible(item, $event)\" style=\"width: max-content; height: 25px\">{{item.get(\'label\')}}</md-checkbox></div><div class=mapa-lista-camadas-title>Informação Nova/Alterada</div><div ng-repeat=\"item in layers.getArray() | filter: layerFilterImovelAlterado\"><md-checkbox ng-checked=item.getVisible(); ng-click=\"alterarLayerVisible(item, $event)\" style=\"width: max-content; height: 25px\">{{item.get(\'label\')}}</md-checkbox></div></div><div class=overlayBenfeitoria></div><div id=overlayPesquisarImovel class=overlayMapaDirective ng-click=direcionarDetalharPesquisar(imovelSelecionado); ng-if=\"action === \'pesquisarImovel\' || action === \'detalharImoveisHomologar\'\"></div><div id=overlayLocalizacao ng-if=\"tabIndex === -1\" class=overlayMapaDirective></div><div id=overlayDadosGerais ng-if=\"tabIndex === 0\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayTerreno ng-if=\"tabIndex === 1\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayTabBenfeitoria ng-if=\"tabIndex === 2\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayUnidadeAutonoma ng-if=\"tabIndex === 3\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayInclusaoBenfeitoria ng-if=\"tabIndex === 4\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayProprietario ng-if=\"tabIndex === 5\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayVerLocalizacao ng-if=\"action === \'verlocalizacao\'\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/ci-mapa/view/ci-mapa-template.html","<div class=div-mapa><div layout=column class=\"mapa-controles mapa-ferramentas\"><md-button class=\"md-icon-button mapa-botoes-icone\" ng-click=alterarVisibilidadeBalaoCamadas(); ng-if=\"tabIndex !== 4\"><ng-md-icon icon=layers style=\"fill: #fff\"><md-tooltip>Camadas</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=marcarPonto(); ng-if=\"tabIndex === -1 && action === \'add\'\"><ng-md-icon icon=room style=\"fill: #fff\"><md-tooltip>Marcar Ponto</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=desenharTerreno(); ng-if=\"tabIndex === 1 && action !== \'detail\' && habilitarDesenho\"><ng-md-icon icon=edit style=\"fill: #fff\"><md-tooltip>Desenhar Terreno</md-tooltip></ng-md-icon></md-button><!--         <md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=\"desenharUnidadeAutonoma();\" ng-if=\"tabIndex === 3 && imovel.tipoImovel.id != 8 && (action === \'add\' || action === \'edit\' ) && habilitarDesenho\"> --><!--md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=\"desenharUnidadeAutonoma();\"\n      ng-if=\"tabIndex === 3 && imovel.tipoImovel.id != 8 && (action === \'add\' || action === \'edit\' )\">\n      <ng-md-icon icon=\"edit\" style=\"fill: #fff;\">\n        <md-tooltip>Desenhar Unidade Autônoma</md-tooltip>\n      </ng-md-icon>\n    </md-button--><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=desenharBenfeitoria(); ng-if=\"tabIndex === 4 && habilitarDesenho && action === \'add\'\"><ng-md-icon icon=edit style=\"fill: #fff\"><md-tooltip>Desenhar Benfeitoria</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=apagar() ng-if=::renderBotaoApagar() ng-show=showBotaoApagar()><ng-md-icon icon=delete style=\"fill: #fff\"><md-tooltip>Apagar</md-tooltip></ng-md-icon></md-button><md-button id=tbn-testada class=\"mapa-botoes-icone md-icon-button\" ng-click=marcarPontoTestada(); ng-if=\"tabIndex === 1 && action !== \'detail\' && habilitarDesenho && showTestada\"><ng-md-icon icon=add_location style=\"fill: #fff\"><md-tooltip>Adicionar Testada</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=voltar() ng-if=\"tabIndex !== 1 && action === \'edit\'\"><ng-md-icon icon=gps_fixed style=\"fill: #fff\"><md-tooltip>Voltar</md-tooltip></ng-md-icon></md-button></div><div class=\"mapa-controles controles-bottom\" layout=row layout-align=\"end end\"><div class=mapa-legenda><div class=mapa-legenda-conteudo style=\"font-size: 11px\" ng-class=\"exibirLegenda ? \'mapa-legenda-conteudo-expandido\': \'\'\" layout=column><div layout=row><div class=\"div-cor div-cinza\"></div>Geometria cadastrada</div><div layout=row><div class=\"div-cor div-azul-escuro\"></div>Geometria cadastrada de Unidade Autônoma</div><div layout=row><div class=\"div-cor div-amarela\"></div>Geometria que está sendo cadastrada</div><div layout=row><div class=\"div-cor div-verde\"></div>Geometria válida</div><div layout=row><div class=\"div-cor div-vermelha\"></div>Geometria inválida</div><div layout=row><div class=\"div-cor div-roxa\"></div>Geometria sobreposta</div></div><md-button class=mapa-legenda-botao ng-click=\"exibirLegenda = !exibirLegenda\">Legenda</md-button></div><div ng-if=showMinimapa class=mapa-minimapa ng-click=alterarBingVisible()><ci-minimapa height={{heightMinimapa}} width={{widthMinimapa}}></ci-minimapa></div></div><div ng-show=listaLayers class=\"mapa-controles mapa-lista-camadas\"><div class=mapa-lista-camadas-title>Camadas</div><div ng-repeat=\"item in layers.getArray() | filter: layerFilter\"><md-checkbox ng-checked=item.getVisible(); ng-click=\"alterarLayerVisible(item.get(\'name\'), $event)\">{{item.get(\'label\')}}</md-checkbox></div></div><div class=overlayBenfeitoria></div><div id=overlayPesquisarImovel class=overlayMapaDirective ng-click=direcionarDetalharPesquisar(imovelSelecionado); ng-if=\"action === \'pesquisarImovel\' || action === \'detalharImoveisHomologar\'\"></div><div id=overlayLocalizacao ng-if=\"tabIndex === -1\" class=overlayMapaDirective></div><div id=overlayDadosGerais ng-if=\"tabIndex === 0\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayTerreno ng-if=\"tabIndex === 1\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayTabBenfeitoria ng-if=\"tabIndex === 2\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayUnidadeAutonoma ng-if=\"tabIndex === 3\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayInclusaoBenfeitoria ng-if=\"tabIndex === 4\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayProprietario ng-if=\"tabIndex === 5\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayVerLocalizacao ng-if=\"action === \'verlocalizacao\'\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div></div><div ng-show=\"bloquearMapa && action === \'add\'\" style=\"height: 100%; width: 100%; position: absolute; z-index: 1; background-color: rgba(0,0,0,0.7); top: 0\"><div ng-if=\"tabIndex === -1\" style=\"display: inline-block;\n    background-color: #ecffe6;\n    border-radius: 20px;\n    margin-left: 6px;\n    padding-right: 5px;\n    color: #4caf50;\n    position: absolute;\n    top: 145px\"><md-button style=\"margin: 0\" class=\"mapa-botoes-icone md-icon-button\" ng-click=desbloquearMapa();><ng-md-icon icon=room style=\"fill: #fff\"><md-tooltip>Marcar Ponto</md-tooltip></ng-md-icon></md-button>Selecione aqui para marcar o ponto</div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/custom-input-file/template/custom-input-file-tmpl.html","<div layout=row layout-align=\"start end\"><input type=file accept={{accept}} ng-model=model style=\"padding-left: 75%; display: none\" name={{name}} ng-required=obrigatorio><md-button ng-if=\"nameIsDefined() && objeto.arquivo && botao\" class=\"md-primary md-raised primeira-maiuscula\" ng-click=baixarDocumento();>Baixar Arquivo</md-button><md-button ng-if=!desabilitado class=\"md-primary md-raised primeira-maiuscula\" ng-click=upload();>Escolher Arquivo {{obrigatorio ? \" *\": \"\"}}</md-button><div ng-show=nameIsDefined() style=\"margin-bottom: 10px; border-bottom: 1px solid #aaa; padding: 5px\">{{nameIsDefined()}}</div><div ng-show=\"!nameIsDefined() && showInput\" style=\"margin-bottom: 10px; border-bottom: 1px solid #aaa; padding: 5px; background-color: #FFDDDD; border-color: #DD2C00; color: #DD2C00\">{{\"Nenhum arquivo selecionado *\"}}</div><ng-md-icon icon=delete style=\"fill:#788489; margin-bottom: 10px\" size=22 alt=\"Excluir Arquivo\" ng-click=remover(); ng-if=\"nameIsDefined() && !desabilitado\"><md-tooltip>Excluir Arquivo</md-tooltip></ng-md-icon></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/homologar/templates/inc-homologar-dados-benfeitoria.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Localização do Acessão/Benfeitoria</th></tr><tr md-row><th md-column width=45%>Original</th><th md-column width=45%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell><inc-mapa-homologar ng-show=isCadastroAlterado() geometria=benfeitorias.benfeitoriaOriginal.geometriaBenfeitoria></inc-mapa-homologar></td><td md-cell><inc-mapa-homologar geometria=benfeitorias.benfeitoriaAlterada.geometriaBenfeitoria dado-alterado=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.geometriaBenfeitoria.coordinates, benfeitorias.benfeitoriaAlterada.geometriaBenfeitoria.coordinates)\"></inc-mapa-homologar></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar localização da benfeitoria\" name=\"{{\'localizacaoBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.geometriaBenfeitoria.coordinates, benfeitorias.benfeitoriaAlterada.geometriaBenfeitoria.coordinates)\" ng-checked=\"isChecked(\'localizacaoBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados da Acessão/Benfeitoria</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Tipo de Acessão/Benfeitoria</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.tipoBenfeitoria.descricao }} / {{ benfeitorias.benfeitoriaOriginal.tipoBenfeitoria.id === 1 ? benfeitorias.benfeitoriaOriginal.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitorias.benfeitoriaOriginal.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.tipoBenfeitoria.id, benfeitorias.benfeitoriaAlterada.tipoBenfeitoria.id) && isDadoDivergente(benfeitorias.benfeitoriaOriginal.tipoBenfeitoria.id === 1 ? benfeitorias.benfeitoriaOriginal.edificacao.tipoEspecializacaoEdificacao.id : benfeitorias.benfeitoriaOriginal.benfeitoriaComplementar.tipoEspecializacaoComplementar.id, benfeitorias.benfeitoriaAlterada.tipoBenfeitoria.id === 1 ? benfeitorias.benfeitoriaAlterada.edificacao.tipoEspecializacaoEdificacao.id : benfeitorias.benfeitoriaAlterada.benfeitoriaComplementar.tipoEspecializacaoComplementar.id) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.tipoBenfeitoria.descricao }} / {{ benfeitorias.benfeitoriaAlterada.tipoBenfeitoria.id === 1 ? benfeitorias.benfeitoriaAlterada.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitorias.benfeitoriaAlterada.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de acessão/benfeitoria\" name=\"{{\'tipoBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.tipoBenfeitoria.id, benfeitorias.benfeitoriaAlterada.tipoBenfeitoria.id) && desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.tipoBenfeitoria.id === 1 ? benfeitorias.benfeitoriaOriginal.edificacao.tipoEspecializacaoEdificacao.id : benfeitorias.benfeitoriaOriginal.benfeitoriaComplementar.tipoEspecializacaoComplementar.id, benfeitorias.benfeitoriaAlterada.tipoBenfeitoria.id === 1 ? benfeitorias.benfeitoriaAlterada.edificacao.tipoEspecializacaoEdificacao.id : benfeitorias.benfeitoriaAlterada.benfeitoriaComplementar.tipoEspecializacaoComplementar.id)\" ng-checked=\"isChecked(\'tipoBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Tipo de Projeto</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.tipoProjeto.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.tipoProjeto.id, benfeitorias.benfeitoriaAlterada.edificacao.tipoProjeto.id) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.tipoProjeto.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de projeto\" name=\"{{\'tipoProjetoBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.tipoProjeto.id, benfeitorias.benfeitoriaAlterada.edificacao.tipoProjeto.id)\" ng-checked=\"isChecked(\'tipoProjetoBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Nome</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.nome }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.nome, benfeitorias.benfeitoriaAlterada.nome) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.nome }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar nome\" name=\"{{\'nomeBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.nome, benfeitorias.benfeitoriaAlterada.nome)\" ng-checked=\"isChecked(\'nomeBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Área Construída</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.areaConstruida }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.areaConstruida, benfeitorias.benfeitoriaAlterada.areaConstruida) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.areaConstruida }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área construída\" name=\"{{\'areaConstruidaBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.areaConstruida, benfeitorias.benfeitoriaAlterada.areaConstruida)\" ng-checked=\"isChecked(\'areaConstruidaBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Área de Projeção</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.areaProjecao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.areaProjecao, benfeitorias.benfeitoriaAlterada.edificacao.areaProjecao) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.areaProjecao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área de projeção\" name=\"{{\'areaProjecaoBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.areaProjecao, benfeitorias.benfeitoriaAlterada.edificacao.areaProjecao)\" ng-checked=\"isChecked(\'areaProjecaoBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><!--alterado de taxa de ocupação para Fração de Ocupação--><td md-cell>Fração de Ocupação</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.taxaOcupacao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.taxaOcupacao, benfeitorias.benfeitoriaAlterada.edificacao.taxaOcupacao) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.taxaOcupacao }}</span></td><td md-cell class=coluna-recusar><!--alterado de taxa de ocupação para Fração de Ocupação--><md-checkbox aria-label=\"Recusar fração de ocupação\" name=\"{{\'taxaOcupacaoBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.taxaOcupacao, benfeitorias.benfeitoriaAlterada.edificacao.taxaOcupacao)\" ng-checked=\"isChecked(\'taxaOcupacaoBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Índice de Aproveitamento</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.indiceAproveitamento }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.indiceAproveitamento, benfeitorias.benfeitoriaAlterada.edificacao.indiceAproveitamento) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.indiceAproveitamento }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar índice de aproveitamento\" name=\"{{\'indiceAproveitamentoBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.indiceAproveitamento, benfeitorias.benfeitoriaAlterada.edificacao.indiceAproveitamento)\" ng-checked=\"isChecked(\'indiceAproveitamentoBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número de Pavimentos</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.numeroPavimentos }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.numeroPavimentos, benfeitorias.benfeitoriaAlterada.edificacao.numeroPavimentos) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.numeroPavimentos }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número de pavimentos\" name=\"{{\'numeroPavimentosBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.numeroPavimentos, benfeitorias.benfeitoriaAlterada.edificacao.numeroPavimentos)\" ng-checked=\"isChecked(\'numeroPavimentosBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número de Dormitórios</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.numeroDormitorios }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.numeroDormitorios, benfeitorias.benfeitoriaAlterada.edificacao.numeroDormitorios) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.numeroDormitorios }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número de dormitórios\" name=\"{{\'numeroDormitoriosBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.numeroDormitorios, benfeitorias.benfeitoriaAlterada.edificacao.numeroDormitorios)\" ng-checked=\"isChecked(\'numeroDormitoriosBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Averbada na Matrícula do Imóvel</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.averbada }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.averbada, benfeitorias.benfeitoriaAlterada.edificacao.averbada) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.averbada }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar averbada\" name=\"{{\'averbadaBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.averbada, benfeitorias.benfeitoriaAlterada.edificacao.averbada)\" ng-checked=\"isChecked(\'averbadaBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Descrição da Averbação</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.descricaoAverbacao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.descricaoAverbacao, benfeitorias.benfeitoriaAlterada.edificacao.descricaoAverbacao) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.descricaoAverbacao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar descrição da averbação\" name=\"{{\'descricaoAverbacaoBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.descricaoAverbacao, benfeitorias.benfeitoriaAlterada.edificacao.descricaoAverbacao)\" ng-checked=\"isChecked(\'descricaoAverbacaoBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Possui Garagem</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.possuiGaragem }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.possuiGaragem, benfeitorias.benfeitoriaAlterada.edificacao.possuiGaragem) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.possuiGaragem }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar posse de garagem\" name=\"{{\'possuiGaragemBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.possuiGaragem, benfeitorias.benfeitoriaAlterada.edificacao.possuiGaragem)\" ng-checked=\"isChecked(\'possuiGaragemBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número de Vagas Cobertas</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.numeroVagasCobertas }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.numeroVagasCobertas, benfeitorias.benfeitoriaAlterada.edificacao.numeroVagasCobertas) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.numeroVagasCobertas }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número de vagas cobertas\" name=\"{{\'numeroVagasCobertasBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.numeroVagasCobertas, benfeitorias.benfeitoriaAlterada.edificacao.numeroVagasCobertas)\" ng-checked=\"isChecked(\'numeroVagasCobertasBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número de Vagas Descobertas</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.numeroVagasDescobertas }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.numeroVagasDescobertas, benfeitorias.benfeitoriaAlterada.edificacao.numeroVagasDescobertas) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.numeroVagasDescobertas }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número de vagas descobertas\" name=\"{{\'numeroVagasDescobertasBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.numeroVagasDescobertas, benfeitorias.benfeitoriaAlterada.edificacao.numeroVagasDescobertas)\" ng-checked=\"isChecked(\'numeroVagasDescobertasBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Possui Elevador</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.possuiElevador }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.possuiElevador, benfeitorias.benfeitoriaAlterada.edificacao.possuiElevador) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.possuiElevador }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar posse de elevador\" name=\"{{\'possuiElevadorBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.possuiElevador, benfeitorias.benfeitoriaAlterada.edificacao.possuiElevador)\" ng-checked=\"isChecked(\'possuiElevadorBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Possui Etiqueta Nacional de Conservação de Energia (ENCE) Geral</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.possuiENCE }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.possuiENCE, benfeitorias.benfeitoriaAlterada.edificacao.possuiENCE) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.possuiENCE }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar posse de ENCE\" name=\"{{\'possuiENCEBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.possuiENCE, benfeitorias.benfeitoriaAlterada.edificacao.possuiENCE)\" ng-checked=\"isChecked(\'possuiENCEBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Tipo</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.tipoEnce.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.tipoEnce.id, benfeitorias.benfeitoriaAlterada.edificacao.tipoEnce.id) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.tipoEnce.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de ENCE\" name=\"{{\'tipoEnceBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.tipoEnce.id, benfeitorias.benfeitoriaAlterada.edificacao.tipoEnce.id)\" ng-checked=\"isChecked(\'tipoEnceBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Tipo de Parcial</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.tipoEnceParcial.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.tipoEnceParcial.id, benfeitorias.benfeitoriaAlterada.edificacao.tipoEnceParcial.id) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.tipoEnceParcial.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de ENCE parcial\" name=\"{{\'tipoEnceParcialBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.tipoEnceParcial.id, benfeitorias.benfeitoriaAlterada.edificacao.tipoEnceParcial.id)\" ng-checked=\"isChecked(\'tipoEnceParcialBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Nível</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.nivelEnce.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.nivelEnce.id, benfeitorias.benfeitoriaAlterada.edificacao.nivelEnce.id) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.nivelEnce.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar nível de ENCE\" name=\"{{\'nivelEnceBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.nivelEnce.id, benfeitorias.benfeitoriaAlterada.edificacao.nivelEnce.id)\" ng-checked=\"isChecked(\'nivelEnceBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Observações Gerais</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.observacoes }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.observacoes, benfeitorias.benfeitoriaAlterada.observacoes) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.observacoes }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar observações\" name=\"{{\'observacoesBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.observacoes, benfeitorias.benfeitoriaAlterada.observacoes)\" ng-checked=\"isChecked(\'observacoesBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Condições de Acessibilidade</th></tr><tr md-row><th md-column width=50%>Campo</th><th md-column width=20%>Original</th><th md-column width=20%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Há vagas próximas dos acessos de circulação de pedrestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.vagasSinalizadas.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.vagasSinalizadas.id, benfeitorias.benfeitoriaAlterada.edificacao.vagasSinalizadas.id) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.vagasSinalizadas.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (vagas)\" name=\"{{\'vagasSinalizadasBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.vagasSinalizadas.id, benfeitorias.benfeitoriaAlterada.edificacao.vagasSinalizadas.id)\" ng-checked=\"isChecked(\'vagasSinalizadasBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.acessoLivreObstaculos.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.acessoLivreObstaculos.id, benfeitorias.benfeitoriaAlterada.edificacao.acessoLivreObstaculos.id) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.acessoLivreObstaculos.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (mobilidade)\" name=\"{{\'acessoLivreObstaculosBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.acessoLivreObstaculos.id, benfeitorias.benfeitoriaAlterada.edificacao.acessoLivreObstaculos.id)\" ng-checked=\"isChecked(\'acessoLivreObstaculosBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.banheiroAcessivel.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.banheiroAcessivel.id, benfeitorias.benfeitoriaAlterada.edificacao.banheiroAcessivel.id) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.banheiroAcessivel.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (banheiro)\" name=\"{{\'banheiroAcessivelBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.banheiroAcessivel.id, benfeitorias.benfeitoriaAlterada.edificacao.banheiroAcessivel.id)\" ng-checked=\"isChecked(\'banheiroAcessivelBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=isCadastroAlterado()>{{ benfeitorias.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.elevadorAcessivel.id, benfeitorias.benfeitoriaAlterada.edificacao.elevadorAcessivel.id) ? \'dado-alterado\' : \'\'\"><span>{{ benfeitorias.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (elevador)\" name=\"{{\'elevadorAcessivelBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.elevadorAcessivel.id, benfeitorias.benfeitoriaAlterada.edificacao.elevadorAcessivel.id)\" ng-checked=\"isChecked(\'elevadorAcessivelBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title>Dados do Medidor</th></tr></thead><tbody md-body><tr md-row><td md-cell><v-accordion><v-pane ng-repeat=\"item in medidores\"><v-pane-header>Medidor</v-pane-header><v-pane-content><inc-homologar-dados-medidor medidores=item index=$index fn-is-cadastro-alterado=isCadastroAlterado() fn-desabilitar-checkbox=\"desabilitarCheckbox(dadoOriginal, dadoAlterado)\" fn-is-dado-divergente=\"isDadoDivergente(dadoOriginal, dadoAlterado)\" exists=isChecked(campo) toggle=alternar(element)></inc-homologar-dados-medidor></v-pane-content></v-pane></v-accordion></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Documentação</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Documentos</td><td md-cell><span ng-show=isCadastroAlterado() ng-repeat=\"item in benfeitorias.benfeitoriaOriginal.edificacao.documentacao\"><a ng-href=\"{{ item.arquivo.url }}\" download=\"{{ item.arquivo.nomeOriginal }}\">{{ item.arquivo.nomeOriginal }}</a></span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.edificacao.documentacao, benfeitorias.benfeitoriaAlterada.edificacao.documentacao) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in benfeitorias.benfeitoriaAlterada.edificacao.documentacao\"><a ng-href=\"{{ item.arquivo.url }}\" download=\"{{ item.arquivo.nomeOriginal }}\">{{ item.arquivo.nomeOriginal }}</a></span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar documentação\" name=\"{{\'documentacaoBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.edificacao.documentacao, benfeitorias.benfeitoriaAlterada.edificacao.documentacao)\" ng-checked=\"isChecked(\'documentacaoBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Plantas da Acessão/Benfeitoria</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Plantas</td><td md-cell><span ng-show=isCadastroAlterado() ng-repeat=\"item in benfeitorias.benfeitoriaOriginal.plantas\"><a ng-href=\"{{ item.url }}\" download=\"{{ item.nomeOriginal }}\">{{ item.nomeOriginal }}</a></span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.plantas, benfeitorias.benfeitoriaAlterada.plantas) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in benfeitorias.benfeitoriaAlterada.plantas\"><a ng-href=\"{{ item.url }}\" download=\"{{ item.nomeOriginal }}\">{{ item.nomeOriginal }}</a></span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar plantas\" name=\"{{\'plantasBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.plantas, benfeitorias.benfeitoriaAlterada.plantas)\" ng-checked=\"isChecked(\'plantasBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Fotos da Acessão/Benfeitoria</th></tr><tr md-row><th md-column width=45%>Original</th><th md-column width=45%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell><span ng-show=isCadastroAlterado()><inc-modal-fotos titulo-modal=\"Fotos da Acessão/Benfeitoria\" fotos=benfeitorias.benfeitoriaOriginal.imagens></inc-modal-fotos></span></td><td md-cell ng-class=\"isDadoDivergente(benfeitorias.benfeitoriaOriginal.imagens, benfeitorias.benfeitoriaAlterada.imagens) ? \'dado-alterado\' : \'\'\"><inc-modal-fotos titulo-modal=\"Fotos da Acessão/Benfeitoria\" fotos=benfeitorias.benfeitoriaAlterada.imagens></inc-modal-fotos></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fotos\" name=\"{{\'fotosBenfeitoria\' + codigo}}\" ng-disabled=\"desabilitarCheckbox(benfeitorias.benfeitoriaOriginal.imagens, benfeitorias.benfeitoriaAlterada.imagens)\" ng-checked=\"isChecked(\'fotosBenfeitoria\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/homologar/templates/inc-homologar-dados-encargo.html","<md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Prazo para Cumprimento</td><td md-cell><span>{{ encargos.encargoOriginal.prazoCumprimento | date:\"dd/MM/yyyy\" }}</span></td><td md-cell ng-class=\"isDadoDivergente(encargos.encargoOriginal.prazoCumprimento, encargos.encargoAlterado.prazoCumprimento) ? \'dado-alterado\' : \'\'\"><span>{{ encargos.encargoAlterado.prazoCumprimento | date:\"dd/MM/yyyy\" }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar prazo para cumprimento\" name=\"{{\'prazoCumprimentoEncargo\' + index}}\" ng-disabled=\"desabilitarCheckbox(encargos.encargoOriginal.prazoCumprimento, encargos.encargoAlterado.prazoCumprimento)\" ng-checked=\"isChecked(\'prazoCumprimentoEncargo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Cumprimento do Encargo</td><td md-cell><span>{{ encargos.encargoOriginal.cumprimento }}</span></td><td md-cell ng-class=\"isDadoDivergente(encargos.encargoOriginal.cumprimento, encargos.encargoAlterado.cumprimento) ? \'dado-alterado\' : \'\'\"><span>{{ encargos.encargoAlterado.cumprimento }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar cumprimento do encargo\" name=\"{{\'cumprimentoEncargo\' + index}}\" ng-disabled=\"desabilitarCheckbox(encargos.encargoOriginal.cumprimento, encargos.encargoAlterado.cumprimento)\" ng-checked=\"isChecked(\'cumprimentoEncargo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/app/directives/homologar/templates/inc-homologar-dados-matricula.html","<md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>UF</td><td md-cell><span>{{ matriculas.matriculaOriginal.uf }}</span></td><td md-cell ng-class=\"isDadoDivergente(matriculas.matriculaOriginal.uf, matriculas.matriculaAlterada.uf) ? \'dado-alterado\' : \'\'\"><span>{{ matriculas.matriculaAlterada.uf }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar UF da matrícula\" name=\"{{\'ufMatricula\' + index}}\" ng-disabled=\"desabilitarCheckbox(matriculas.matriculaOriginal.uf, matriculas.matriculaAlterada.uf)\" ng-checked=\"isChecked(\'ufMatricula\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Município</td><td md-cell><span>{{ matriculas.matriculaOriginal.municipio }}</span></td><td md-cell ng-class=\"isDadoDivergente(matriculas.matriculaOriginal.municipio, matriculas.matriculaAlterada.municipio) ? \'dado-alterado\' : \'\'\"><span>{{ matriculas.matriculaAlterada.municipio }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar município da matrícula\" name=\"{{\'municipioMatricula\' + index}}\" ng-disabled=\"desabilitarCheckbox(matriculas.matriculaOriginal.municipio, matriculas.matriculaAlterada.municipio)\" ng-checked=\"isChecked(\'municipioMatricula\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Nome do Cartório</td><td md-cell><span>{{ matriculas.matriculaOriginal.cartorioID }}</span></td><td md-cell ng-class=\"isDadoDivergente(matriculas.matriculaOriginal.cartorioID, matriculas.matriculaAlterada.cartorioID) ? \'dado-alterado\' : \'\'\"><span>{{ matriculas.matriculaAlterada.cartorioID }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar nome do cartório\" name=\"{{\'nomeCartorioMatricula\' + index}}\" ng-disabled=\"desabilitarCheckbox(matriculas.matriculaOriginal.cartorioID, matriculas.matriculaAlterada.cartorioID)\" ng-checked=\"isChecked(\'nomeCartorioMatricula\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Identificação do Cartório</td><td md-cell><span>{{ matriculas.matriculaOriginal.cartorioEstrangeiro }}</span></td><td md-cell ng-class=\"isDadoDivergente(matriculas.matriculaOriginal.cartorioEstrangeiro, matriculas.matriculaAlterada.cartorioEstrangeiro) ? \'dado-alterado\' : \'\'\"><span>{{ matriculas.matriculaAlterada.cartorioEstrangeiro }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar identificação do cartório\" name=\"{{\'identificacaoCartorioMatricula\' + index}}\" ng-disabled=\"isEquals(matriculas.matriculaOriginal.cartorioEstrangeiro, matriculas.matriculaAlterada.cartorioEstrangeiro)\" ng-checked=\"isChecked(\'identificacaoCartorioMatricula\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Data da Matrícula</td><td md-cell><span>{{ matriculas.matriculaOriginal.dataMatricula | date:\"dd/MM/yyyy\" }}</span></td><td md-cell ng-class=\"isDadoDivergente(matriculas.matriculaOriginal.dataMatricula, matriculas.matriculaAlterada.dataMatricula) ? \'dado-alterado\' : \'\'\"><span>{{ matriculas.matriculaAlterada.dataMatricula | date:\"dd/MM/yyyy\" }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar data da matrícula\" name=\"{{\'dataMatricula\' + index}}\" ng-disabled=\"desabilitarCheckbox(matriculas.matriculaOriginal.dataMatricula, matriculas.matriculaAlterada.dataMatricula)\" ng-checked=\"isChecked(\'dataMatricula\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Data do Registro</td><td md-cell><span>{{ matriculas.matriculaOriginal.dataRegistro | date:\"dd/MM/yyyy\" }}</span></td><td md-cell ng-class=\"isDadoDivergente(matriculas.matriculaOriginal.dataRegistro, matriculas.matriculaAlterada.dataRegistro) ? \'dado-alterado\' : \'\'\"><span>{{ matriculas.matriculaAlterada.dataRegistro | date:\"dd/MM/yyyy\" }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar data do registro\" name=\"{{\'dataRegistroMatricula\' + index}}\" ng-disabled=\"desabilitarCheckbox(matriculas.matriculaOriginal.dataRegistro, matriculas.matriculaAlterada.dataRegistro)\" ng-checked=\"isChecked(\'dataRegistroMatricula\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/app/directives/homologar/templates/inc-homologar-dados-medidor.html","<md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Tipo de Medidor</td><td md-cell><span>{{ medidores.medidorOriginal.empresaFornecedora.tipoFornecedoraEnum.tipoFornecedora }}</span></td><td md-cell ng-class=\"isDadoDivergente(medidores.medidorOriginal.empresaFornecedora.tipoFornecedoraEnum.name, medidores.medidorAlterado.empresaFornecedora.tipoFornecedoraEnum.name) ? \'dado-alterado\' : \'\'\"><span>{{ medidores.medidorAlterado.empresaFornecedora.tipoFornecedoraEnum.tipoFornecedora }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de medidor\" name=\"{{\'tipoMedidor\' + index}}\" ng-disabled=\"desabilitarCheckbox(medidores.medidorOriginal.empresaFornecedora.tipoFornecedoraEnum.name, medidores.medidorAlterado.empresaFornecedora.tipoFornecedoraEnum.name)\" ng-checked=\"isChecked(\'tipoMedidor\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Código do Medidor</td><td md-cell><span>{{ medidores.medidorOriginal.codigo }}</span></td><td md-cell ng-class=\"isDadoDivergente(medidores.medidorOriginal.codigo, medidores.medidorAlterado.codigo) ? \'dado-alterado\' : \'\'\"><span>{{ medidores.medidorAlterado.codigo }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar código do medidor\" name=\"{{\'codigoMedidor\' + index}}\" ng-disabled=\"desabilitarCheckbox(medidores.medidorOriginal.codigo, medidores.medidorAlterado.codigo)\" ng-checked=\"isChecked(\'codigoMedidor\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Empresa Fornecedora</td><td md-cell><span>{{ medidores.medidorOriginal.empresaFornecedora.nome }}</span></td><td md-cell ng-class=\"isDadoDivergente(medidores.medidorOriginal.empresaFornecedora.nome, medidores.medidorAlterado.empresaFornecedora.nome) ? \'dado-alterado\' : \'\'\"><span>{{ medidores.medidorAlterado.empresaFornecedora.nome }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar empresa fornecedora\" name=\"{{\'empresaFornecedoraMedidor\' + index}}\" ng-disabled=\"desabilitarCheckbox(medidores.medidorOriginal.empresaFornecedora.nome, medidores.medidorAlterado.empresaFornecedora.nome)\" ng-checked=\"isChecked(\'empresaFornecedoraMedidor\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/app/directives/homologar/templates/inc-homologar-dados-titulo-aquisitivo.html","<md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Tipo</td><td md-cell><span>{{ titulos.tituloOriginal.tipoTituloAquisitivo.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.tipoTituloAquisitivo.id, titulos.tituloAlterado.tipoTituloAquisitivo.id) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.tipoTituloAquisitivo.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de título aquisitivo\" name=\"{{\'tipoTituloAquisitivo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.tipoTituloAquisitivo.id, titulos.tituloAlterado.tipoTituloAquisitivo.id)\" ng-checked=\"isChecked(\'tipoTituloAquisitivo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Tipo de Ato Legal</td><td md-cell><span>{{ titulos.tituloOriginal.tipoAtoLegal.descricao }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.tipoAtoLegal.id, titulos.tituloAlterado.tipoAtoLegal.id) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.tipoAtoLegal.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de ato legal\" name=\"{{\'tipoAtoLegal\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.tipoAtoLegal.id, titulos.tituloAlterado.tipoAtoLegal.id)\" ng-checked=\"isChecked(\'tipoAtoLegal\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número</td><td md-cell><span>{{ titulos.tituloOriginal.numero }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.numero, titulos.tituloAlterado.numero) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.numero }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número\" name=\"{{\'numeroTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.numero, titulos.tituloAlterado.numero)\" ng-checked=\"isChecked(\'numeroTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Nome</td><td md-cell><span>{{ titulos.tituloOriginal.nome }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.nome, titulos.tituloAlterado.nome) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.nome }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar nome\" name=\"{{\'nomeTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.nome, titulos.tituloAlterado.nome)\" ng-checked=\"isChecked(\'nomeTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Arquivo</td><td md-cell><span><a ng-href=\"{{ titulos.tituloOriginal.url }}\" download=\"{{ titulos.tituloOriginal.nomeArquivo }}\">{{ titulos.tituloOriginal.nomeArquivo }}</a></span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.nomeArquivo, titulos.tituloAlterado.nomeArquivo) ? \'dado-alterado\' : \'\'\"><a ng-href=\"{{ titulos.tituloAlterado.url }}\" download=\"{{ titulos.tituloAlterado.nomeArquivo }}\">{{ titulos.tituloAlterado.nomeArquivo }}</a></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar arquivo\" name=\"{{\'arquivoTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.nomeArquivo, titulos.tituloAlterado.nomeArquivo)\" ng-checked=\"isChecked(\'arquivoTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Data da Assinatura</td><td md-cell><span>{{ titulos.tituloOriginal.dataAssinatura | date:\"dd/MM/yyyy\" }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.dataAssinatura, titulos.tituloAlterado.dataAssinatura) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.dataAssinatura | date:\"dd/MM/yyyy\" }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar data da assinatura\" name=\"{{\'dataAssinaturaTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.dataAssinatura, titulos.tituloAlterado.dataAssinatura)\" ng-checked=\"isChecked(\'dataAssinaturaTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Data do Final da Vigência</td><td md-cell><span>{{ titulos.tituloOriginal.dataFinalVigencia | date:\"dd/MM/yyyy\" }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.dataFinalVigencia, titulos.tituloAlterado.dataFinalVigencia) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.dataFinalVigencia | date:\"dd/MM/yyyy\" }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar data do final da vigência\" name=\"{{\'dataFinalVigenciaTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.dataFinalVigencia, titulos.tituloAlterado.dataFinalVigencia)\" ng-checked=\"isChecked(\'dataFinalVigenciaTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Data da Prorrogação da Vigência</td><td md-cell><span>{{ titulos.tituloOriginal.dataProrrogacaoVigencia | date:\"dd/MM/yyyy\" }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.dataProrrogacaoVigencia, titulos.tituloAlterado.dataProrrogacaoVigencia) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.dataProrrogacaoVigencia | date:\"dd/MM/yyyy\" }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar data da prorrogação da vigência\" name=\"{{\'dataProrrogacaoVigenciaTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.dataProrrogacaoVigencia, titulos.tituloAlterado.dataProrrogacaoVigencia)\" ng-checked=\"isChecked(\'dataProrrogacaoVigenciaTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Data da Prorrogação da Vigência</td><td md-cell><span>{{ titulos.tituloOriginal.dataProrrogacaoVigencia | date:\"dd/MM/yyyy\" }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.dataProrrogacaoVigencia, titulos.tituloAlterado.dataProrrogacaoVigencia) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.dataProrrogacaoVigencia | date:\"dd/MM/yyyy\" }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar data da prorrogação da vigência\" name=\"{{\'dataProrrogacaoVigenciaTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.dataProrrogacaoVigencia, titulos.tituloAlterado.dataProrrogacaoVigencia)\" ng-checked=\"isChecked(\'dataProrrogacaoVigenciaTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Página</td><td md-cell><span>{{ titulos.tituloOriginal.publicacao.pagina }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.publicacao.pagina, titulos.tituloAlterado.publicacao.pagina) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.publicacao.pagina }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar página da publicação\" name=\"{{\'paginaPublicacaoTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.publicacao.pagina, titulos.tituloAlterado.publicacao.pagina)\" ng-checked=\"isChecked(\'paginaPublicacaoTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Seção</td><td md-cell><span>{{ titulos.tituloOriginal.publicacao.secao }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.publicacao.secao, titulos.tituloAlterado.publicacao.secao) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.publicacao.secao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar seção da publicação\" name=\"{{\'secaoPublicacaoTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.publicacao.secao, titulos.tituloAlterado.publicacao.secao)\" ng-checked=\"isChecked(\'secaoPublicacaoTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Data</td><td md-cell><span>{{ titulos.tituloOriginal.publicacao.dataPublicacao }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.publicacao.dataPublicacao, titulos.tituloAlterado.publicacao.dataPublicacao) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.publicacao.dataPublicacao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar data da publicação\" name=\"{{\'dataPublicacaoTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.publicacao.dataPublicacao, titulos.tituloAlterado.publicacao.dataPublicacao)\" ng-checked=\"isChecked(\'dataPublicacaoTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Moeda</td><td md-cell><span>{{ titulos.tituloOriginal.moeda }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.moeda, titulos.tituloAlterado.moeda) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.moeda }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar moeda\" name=\"{{\'moedaTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.moeda, titulos.tituloAlterado.moeda)\" ng-checked=\"isChecked(\'moedaTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Valor do Contrato</td><td md-cell><span>{{ titulos.tituloOriginal.valorContrato }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.valorContrato, titulos.tituloAlterado.valorContrato) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.valorContrato }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar valor do contrato\" name=\"{{\'valorContratoTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.valorContrato, titulos.tituloAlterado.valorContrato)\" ng-checked=\"isChecked(\'valorContratoTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Observações/Anotações</td><td md-cell><span>{{ titulos.tituloOriginal.observacao }}</span></td><td md-cell ng-class=\"isDadoDivergente(titulos.tituloOriginal.observacao, titulos.tituloAlterado.observacao) ? \'dado-alterado\' : \'\'\"><span>{{ titulos.tituloAlterado.observacao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar observações/anotações\" name=\"{{\'observacaoTitulo\' + index}}\" ng-disabled=\"desabilitarCheckbox(titulos.tituloOriginal.observacao, titulos.tituloAlterado.observacao)\" ng-checked=\"isChecked(\'observacaoTitulo\')\" ng-click=alternar($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell colspan=4><v-accordion><v-pane ng-repeat=\"item in encargos\"><v-pane-header>Encargo - {{ item.encargoOriginal ? item.encargoOriginal.descricao : item.encargoAlterado.descricao }}</v-pane-header><v-pane-content><inc-homologar-dados-encargo encargos=item index=$index fn-is-cadastro-alterado=isCadastroAlterado() fn-desabilitar-checkbox=\"desabilitarCheckbox(dadoOriginal, dadoAlterado)\" fn-is-dado-divergente=\"isDadoDivergente(dadoOriginal, dadoAlterado)\" exists=isChecked(campo) toggle=alternar(element)></inc-homologar-dados-encargo></v-pane-content></v-pane></v-accordion></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/app/directives/seleciona-rips-modal/view/seleciona-rip-template-modal.html","<md-dialog aria-label=\"Selecionar Imoveis\" flex=80 ng-cloak><form name=selecionarRIPsForm><md-toolbar><div class=md-toolbar-tools><h2>Selecionar Imóveis</h2><span flex></span><md-button class=md-icon-button ng-click=fechar();><md-icon md-svg-src=assets/img/icons/ic_close_white_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><md-input-container class=md-block flex><label>País</label><md-select ng-model=filtroRIPs.enderecamento.pais name=pais ng-change=buscarCidadesPorPais(); ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option ng-repeat=\"item in paises | orderByBr:\'descricao\'\" ng-value=item ng-selected=\"item.id == 1\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block ng-show=\"filtroRIPs.enderecamento.pais.id === 1\" flex><label>CEP</label><input ng-model=filtroRIPs.enderecamento.cep ui-br-cep-mask ng-change=buscarEnderecoCep();></md-input-container><md-input-container class=md-block ng-show=\"filtroRIPs.enderecamento.pais.id === 1\" flex><label id=ufRipLabel>UF</label><!--<md-select ng-model=\"filtroRIPs.enderecamento.uf\" ng-disabled=\"isCEPPreenchido\" name=\"uf\"\n              ng-change=\"buscarMunicipiosPorUf()\">\n              <md-option ng-repeat=\"uf in ufs\" ng-value=\"uf.sigla\">\n                {{uf.descricao}}\n              </md-option>\n            </md-select>--><md-autocomplete name=ufRip ng-disabled=isCEPPreenchido md-selected-item-change=buscarMunicipiosPorUf() md-clear-button=!isCEPPreenchido md-selected-item=filtroRIPs.enderecamento.uf md-search-text=searchUfRip md-items=\"uf in ufs | filter : {sigla:searchUfRip} | orderByBr:\'sigla\'\" md-item-text=uf.sigla md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=ufRipLabel><md-item-template><span md-highlight-text=searchUfRip md-highlight-flags=^i>{{uf.sigla}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{searchUfRip}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container></div><div layout-gt-sm=row layout-margin><md-input-container class=md-block ng-show=\"filtroRIPs.enderecamento.pais.id === 1\" flex=50><label id=municipioRipLabel>Município</label><!--<md-select ng-model=\"filtroRIPs.enderecamento.municipio\" name=\"municipio\"\n              ng-disabled=\"municipios.length == 0 || isCEPPreenchido\">\n              <md-option ng-repeat=\"item in municipios\" ng-value=\"item.nome ? item.nome : item\">\n                {{item.nome ? item.nome : item}}\n              </md-option>\n            </md-select>--><md-autocomplete md-no-cache=true name=municipio ng-disabled=\"municipios.length == 0 || isCEPPreenchido\" md-selected-item-change=selecionaMunicipio() md-clear-button=\"municipios.length > 0 && !isCEPPreenchido\" md-selected-item=filtroRIPs.enderecamento.municipio md-search-text=searchMunicipioRip md-items=\"item in municipios | filter : {nome:searchMunicipioRip} | orderByBr:\'nome\'\" md-item-text=item.nome md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=municipioRipLabel><md-item-template><span md-highlight-text=searchMunicipioRip md-highlight-flags=^i>{{item.nome}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{searchMunicipioRip}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block ng-show=\"filtroRIPs.enderecamento.pais.id != 1\" flex><label>Cidade Exterior</label><md-select ng-model=filtroRIPs.enderecamento.cidadeExterior name=cidadeExterior><md-option ng-repeat=\"item in cidades\" ng-value=item>{{item}}</md-option></md-select></md-input-container></div><div layout=row layout-margin layout-align=\"end center\"><md-button class=\"md-raised md-primary\" ng-click=limpar()>Limpar</md-button><md-button class=\"md-raised md-primary\" ng-click=pesquisar()>Pesquisar</md-button></div><div layout=row layout-align=\"center stretch\" ng-show=\"resultadoPesquisa.length > 0 && multipleSelection\"><div flex=90><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column><md-checkbox style=\"background-color: rgba(255, 255, 255, 255)\" ng-model=selecionarTodos ng-change=marcarDesmarcarTodosCheckbox(); aria-label=\"Selecionar Todos\"></md-checkbox></th><th md-column>Detalhes</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in resultadoPesquisa\"><td md-cell><md-checkbox aria-label={{item.rip}} ng-model=item.isSelecionado ng-change=atualizaMarcarTodosCheckbox(item);></md-checkbox></td><td md-cell><p><span style=\"font-weight: bold\">RIP: </span>{{ item.rip }}</p><p ng-show=\"item.enderecoDTO.pais == \'Brasil\'\"><span style=\"font-weight: bold\">Endereço: </span>{{ item.enderecoDTO.tipoLogradouro + \" \" + item.enderecoDTO.logradouro + \", \" + item.enderecoDTO.numero + \", \" + item.enderecoDTO.bairro + \", \" + item.enderecoDTO.municipio + \"-\" + item.enderecoDTO.uf + \", Brasil, CEP:\" + item.enderecoDTO.cep }}</p><p ng-show=\"item.enderecoDTO.pais != \'Brasil\'\"><span style=\"font-weight: bold\">Endereço: </span>{{ item.enderecoDTO.endereco + \", \" + item.enderecoDTO.cidadeExterior + \", \" + item.enderecoDTO.pais + \", Código Postal:\" + item.enderecoDTO.codigoPostal }}</p><p><span style=\"font-weight: bold\">Matricula/Transcrição: </span>{{ item.numeroMatricula || \"-\" }}</p><p><span style=\"font-weight: bold\">Proprietário/Titular: </span>{{ item.tipoProprietarioReal }}</p></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=irParaDetalhamento(item.id) class=\"md-fab md-raised md-mini\" aria-label=\"Detalhar Imóvel\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Detalhar Imóvel</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacao.limit md-limit-options=paginacao.limitOptions md-page=paginacao.page md-total={{paginacao.totalElements}} md-on-paginate=page md-page-select ng-disabled=\"paginacao.totalElements < 5\"></md-table-pagination></div></div><div layout-gt-sm=row layout-align=end ng-show=\"resultadoPesquisa.length > 0 && multipleSelection\"><md-button class=\"md-raised md-primary\" ng-click=confirmar()>Confirmar</md-button></div><div layout=row layout-align=\"center stretch\" ng-show=\"resultadoPesquisa.length > 0 && !multipleSelection\"><div flex=90><md-radio-group ng-model=selected><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column></th><th md-column>Detalhes</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in resultadoPesquisa\"><td md-cell><md-radio-button ng-value=item></md-radio-button></td><td md-cell><p><span style=\"font-weight: bold\">RIP: </span>{{ item.rip }}</p><p ng-show=\"item.enderecoDTO.pais == \'Brasil\'\"><span style=\"font-weight: bold\">Endereço: </span>{{ item.enderecoDTO.tipoLogradouro + \" \" + item.enderecoDTO.logradouro + \", \" + item.enderecoDTO.numero + \", \" + item.enderecoDTO.bairro + \", \" + item.enderecoDTO.municipio + \"-\" + item.enderecoDTO.uf + \", Brasil, CEP:\" + item.enderecoDTO.cep }}</p><p ng-show=\"item.enderecoDTO.pais != \'Brasil\'\"><span style=\"font-weight: bold\">Endereço: </span>{{ item.enderecoDTO.endereco + \", \" + item.enderecoDTO.complemento + \", \" + item.enderecoDTO.cidadeExterior + \", \" + item.enderecoDTO.pais + \", Código Postal:\" + item.enderecoDTO.codigoPostal }}</p><p><span style=\"font-weight: bold\">Matricula/Transcrição: </span>{{ item.numeroMatricula || \"-\" }}</p><p><span style=\"font-weight: bold\">Proprietário/Titular: </span>{{ item.tipoProprietarioReal }}</p></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=irParaDetalhamento(item.id) class=\"md-fab md-raised md-mini\" aria-label=\"Detalhar Imóvel\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Detalhar Imóvel</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container></md-radio-group><md-table-pagination md-limit=paginacao.limit md-limit-options=paginacao.limitOptions md-page=paginacao.page md-total=\"{{ paginacao.totalElements }}\" md-on-paginate=page md-page-select ng-disabled=\"paginacao.totalElements < 5\"></md-table-pagination></div></div><div layout-gt-sm=row layout-align=end ng-show=\"resultadoPesquisa.length > 0 && !multipleSelection\"><md-button class=\"md-raised md-primary\" ng-click=selecionarRip()>Confirmar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/app/directives/upload-arquivo/templates/modal-upload-arquivo.html","<md-dialog aria-label=\"Metadados do Arquivo de Geometria\" flex=60 ng-cloak><ng-form name=forms.uploadArquivo><md-toolbar><div class=md-toolbar-tools><h2>Metadados do Arquivo de Geometria</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div ng-show=\"(!arquivo || !arquivo.arquivo) && !oldArquivo\" layout=row layout-margin layout-align=\"start center\"><md-input-container ng-class=\"forms.uploadArquivo.arquivo.$error.required && submittedFormUploadArquivo ? \'md-input-invalid\':\'\'\" class=md-input-has-placeholder><input id=input-file type=file ngf-select ng-model=fileUploaded name=arquivo accept=.zip,.csv,.txt ngf-max-size=10MB ngf-model-invalid=errorFile ng-change=selecionaArquivo() placeholder=\"Arquivo (.zip, .csv ou .txt)\"></md-input-container></div><div ng-if=\"(arquivo && arquivo.arquivo) || oldArquivo\" layout=row layout-margin layout-align=\"start center\"><md-button ng-if=!oldArquivo ng-click=download() class=\"md-raised md-primary\">Baixar Arquivo</md-button><md-button ng-click=novoArquivo() class=\"md-raised md-primary\" ng-disabled=detail>Upload de Novo Arquivo</md-button><span>{{!oldArquivo ? arquivo.nome : fileUploaded.name}}</span><ng-md-icon ng-if=oldArquivo icon=delete style=fill:#788489 size=18 alt=\"Excluir Arquivo\" ng-click=excluirArquivo() ng-disabled=detail></ng-md-icon></div><div layout=row layout-margin layout-align=\"start center\"><md-input-container ng-class=\"forms.uploadArquivo.dataPlanta.$error.required && submittedFormUploadArquivo ? \'md-input-invalid\':\'\'\" flex=40><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_UPLOAD_ARQ_TERRENO_DT_ARQ ng-model=arquivo.dataPlanta ng-required=true md-max-date=hoje name=dataPlanta md-placeholder=\"Selecione a data\" ng-disabled=detail></md-datepicker></md-input-container><md-input-container ng-show=\"arquivo.tipoFormaLevantamento.id != 3\" ng-class=\"forms.uploadArquivo.escala.$error.required && submittedFormUploadArquivo ? \'md-input-invalid\':\'\'\" layout=row class=md-input-has-placeholder flex=60 style=\"margin-bottom: 30px\"><div layout=column layout-align=\"center end\">1:</div><input campo-ajuda=INC_FORM_UPLOAD_ARQ_TERRENO_ESCALA ng-model=arquivo.escala ng-required=\"arquivo.tipoFormaLevantamento.id != 3\" placeholder=\"Escala de Vetorização\" type=text name=escala ui-number-mask=0 ui-hide-group-sep maxlength=9 ng-disabled=detail></md-input-container></div><div layout=row layout-margin layout-align=\"start center\"><md-input-container ng-class=\"forms.uploadArquivo.formaLevantamento.$error.required && submittedFormUploadArquivo ? \'md-input-invalid\':\'\'\" flex><label>Forma de Levantamento</label><md-select campo-ajuda=INC_FORM_UPLOAD_ARQ_TERRENO_FORM_LEVAN name=formaLevantamento ng-model=arquivo.tipoFormaLevantamento ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=true ng-change=handleFormaLevantamentoSelected() ng-disabled=detail><md-option campo-ajuda={{item.codigoSistemico}} ng-value=item ng-repeat=\"item in formasLevantamento| orderByBr:\'descricao\'\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-class=\"forms.uploadArquivo.resolucaoEspacial.$error.required && submittedFormUploadArquivo ? \'md-input-invalid\':\'\'\" ng-show=\"arquivo.tipoFormaLevantamento.id == 1 || arquivo.tipoFormaLevantamento.id == 2\" style=\"margin-top: 30px\" flex><input campo-ajuda=INC_FORM_UPLOAD_ARQ_TERRENO_RES_ESPAC ng-model=arquivo.resolucaoEspacial ui-number-mask ng-required=\"arquivo.tipoFormaLevantamento.id == 1 || arquivo.tipoFormaLevantamento.id == 2\" name=resolucaoEspacial placeholder=\"Resolução Espacial(m)\" ng-disabled=detail></md-input-container><md-input-container ng-class=\"forms.uploadArquivo.tipoEquipamento.$error.required && submittedFormUploadArquivo ? \'md-input-invalid\':\'\'\" class=md-block flex-gt-sm ng-show=\"arquivo.tipoFormaLevantamento.id == 3\" flex><label>Tipo de Equipamento Utilizado</label><md-select campo-ajuda=\"Tipo de Equipamento Utilizado\" name=tipoEquipamento ng-model=arquivo.tipoEquipamento ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"arquivo.tipoFormaLevantamento.id == 3\" ng-disabled=detail><md-option campo-ajuda={{item.codigoSistemico}} ng-value=item ng-repeat=\"item in tiposEquipamento| orderByBr:\'descricao\'\">{{item.descricao}}</md-option></md-select></md-input-container></div></div></md-dialog-content><md-dialog-actions layout=row layout-align=\"end right\" style=\"margin-bottom: 20px\"><md-button ng-click=fechar() class=md-raised>Voltar</md-button><md-button ng-click=confirmar() style=\"margin-right: 20px\" class=\"md-raised md-primary\" ng-if=!detail>Confirmar</md-button></md-dialog-actions></ng-form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/app/directives/upload-arquivo/templates/upload-arquivo.html","<div layout=row layout-align=\"start center\"><md-button id=btnEscolherArquivo1 class=\"md-raised md-primary primeira-maiuscula\" ng-click=verificarExistenciaDeArquivo($event) ng-disabled=isDisabled><span ng-show=\"arquivo && arquivo.arquivo\">Ver dados do arquivo vetorial </span><span ng-hide=\"arquivo && arquivo.arquivo\">Escolher Arquivo</span></md-button><div layout=row ng-if=\"arquivo.nome && arquivo.arquivo\"><span ng-bind=arquivo.nome></span><ng-md-icon icon=delete style=\"fill: #788489\" size=18 alt=\"Excluir Arquivo\" ng-click=excluirArquivo($event)></ng-md-icon></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/utils/templates/inc-mensagem.html","<div class=inc-mensagem-container style={{estilo}} ng-class=classe layout=row><md-icon ng-if=showIcon() ng-bind=icone></md-icon><div layout=column flex=100><span class=inc-mensagem-titulo ng-bind=titulo></span><div layout=column ng-transclude></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/utils/templates/inc-modal-fotos.html","<md-button ng-show=\"fotos.length > 0\" ng-click=exibirModal($event)>Ver fotos ({{ fotos.length }})</md-button>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/Untitled-1.json","{ \"numeroProcesso\": \"36269\", \"usuarioCadastrador\": 1, \"enderecamento\": { \"cep\": \"70337050\", \"stLocalidade\": \"1\", \"uf\": \"DF\", \"municipio\": \"Brasília\", \"codMunicipio\": \"5300108\", \"tipoLogradouro\": \"Quadra\", \"logradouro\": \"SQS 304 Bloco E\", \"bairro\": \"Asa Sul\", \"geometria\": { \"type\": \"MultiPolygon\", \"coordinates\": [ [ [ [-5343384.081686566, -1746869.8343221222], [-5305895.939944649, -1746948.5464176352], [-5305753.272106012, -1746948.8459641421], [-5301132.008977814, -1746948.2450228045], [-5294653.44593555, -1746945.8583547373], [-5294580.472558434, -1746945.8314382944], [-5287724.4023023825, -1746941.416796139], [-5287233.139148118, -1746937.439748935], [-5287221.6200298555, -1746937.5817244388], [-5278469.356722093, -1746927.9060207922], [-5278511.932197184, -1752302.1891562124], [-5278494.696377784, -1752357.1788089543], [-5278268.093643085, -1752435.5820305853], [-5278140.2665850045, -1752423.5198819009], [-5278041.491019717, -1752363.2098079408], [-5277948.525775245, -1752333.0548282804], [-5277838.129679621, -1752320.9928471232], [-5277721.923151867, -1752387.3338191852], [-5277611.526944922, -1752441.6130494424], [-5277472.079245198, -1752417.488867619], [-5277390.734642372, -1752339.0858211517], [-5277309.3901508665, -1752290.837920962], [-5277164.132019001, -1752272.7449836084], [-5277030.494528774, -1752302.8998868337], [-5276914.288001012, -1752327.023836938], [-5276815.512547042, -1752357.1788089543], [-5276786.460942932, -1752411.4578548775], [-5276809.702226224, -1752489.8612553545], [-5276832.943509507, -1752580.3269051379], [-5276821.32286787, -1752646.6687158018], [-5276774.840189976, -1752725.0727757881], [-5276681.875056814, -1752785.3838825522], [-5276600.530453991, -1752809.5083218375], [-5276495.944679193, -1752809.5083218375], [-5276402.979434724, -1752869.819527097], [-5276368.1175097935, -1752990.4425118663], [-5276420.410397198, -1753062.8165269962], [-5276437.841359652, -1753135.1908778758], [-5276437.841359652, -1753207.56533343], [-5276461.082754263, -1753340.2525228006], [-5276501.755000022, -1753418.6588689208], [-5276478.5137167275, -1753485.003017703], [-5276333.255584866, -1753497.0655890875], [-5276199.618094636, -1753533.2533399404], [-5276106.652850167, -1753605.629006862], [-5276048.549641953, -1753726.2557234531], [-5276042.739321125, -1753889.1027211102], [-5276002.067075367, -1754009.7308758395], [-5275903.291510079, -1754094.1708674342], [-5275781.274772817, -1754160.5167854964], [-5275641.826961772, -1754287.1778042892], [-5275572.103000594, -1754395.7449454812], [-5275455.896584149, -1754558.5965292805], [-5275333.879735567, -1754643.038470627], [-5275223.483528633, -1754655.1016295874], [-5275101.466791372, -1754600.8174624513], [-5275008.501546901, -1754606.8490304516], [-5274909.725981612, -1754649.0700493408], [-5274793.519565168, -1754661.1332113654], [-5274677.313037406, -1754643.038470627], [-5274590.158113763, -1754618.9121710544], [-5274503.0033014305, -1754618.9121710544], [-5274410.038056959, -1754673.196379529], [-5274357.745169567, -1754817.9549905176], [-5274328.693565454, -1755016.9994771315], [-5274288.021319697, -1755203.9821873596], [-5274235.728320984, -1755354.775789842], [-5274136.952867014, -1755511.602147134], [-5273951.022489394, -1755668.4295397575], [-5273741.85071716, -1755764.939263532], [-5273550.11001872, -1755861.4492639035], [-5273329.31771616, -1755933.8321370904], [-5273184.0595842935, -1755970.0235986386], [-5273015.560169137, -1755957.9597719845], [-5272835.440112336, -1755903.6725123709], [-5272678.561338823, -1755855.4173773846], [-5272486.820640384, -1755843.353608956], [-5272324.131546045, -1755927.8002321895], [-5272236.976622394, -1756036.3747546899], [-5272109.149564313, -1756102.7262117076], [-5271981.322394912, -1756199.2375847471], [-5271824.443621403, -1756205.269558583], [-5271731.4783769315, -1756132.885973649], [-5271679.185489529, -1756030.3428237555], [-5271586.220356378, -1755988.1193500774], [-5271475.824149442, -1755988.1193500774], [-5271371.238263324, -1756048.4386211652], [-5271278.273130175, -1756132.885973649], [-5271144.635639956, -1756163.045773895], [-5271051.670395486, -1756150.9818491885], [-5270964.515583154, -1756078.598314163], [-5270912.222695759, -1756018.2789664709], [-5270790.205847167, -1755994.1512702988], [-5270703.050923524, -1756018.2789664709], [-5270569.413544617, -1756126.8540181916], [-5270464.82765851, -1756199.2375847471], [-5270325.379847463, -1756253.5254044516], [-5270203.362998881, -1756356.069518108], [-5270122.018507368, -1756506.8704353517], [-5270034.863583726, -1756591.3193026586], [-5269930.277808928, -1756585.2872307494], [-5269814.071281164, -1756615.4477211975], [-5269750.1577521255, -1756699.8969747308], [-5269633.951224371, -1756772.2822882885], [-5269482.882771679, -1756832.603667078], [-5269366.6762439255, -1756947.2144895117], [-5269203.987149587, -1756995.4719590372], [-5269041.298055247, -1756971.3432697954], [-5268901.850355522, -1756935.1501663618], [-5268814.69543188, -1756953.2466533852], [-5268721.730298729, -1757025.6328550677], [-5268622.954733439, -1757146.2769380591], [-5268471.886280736, -1757206.5991517243], [-5268338.248790519, -1757164.373586061], [-5268204.6113002915, -1757073.8903684474], [-5268041.922317281, -1757043.7294110875], [-5267873.422902125, -1756995.4719590372], [-5267561.6020915955, -1757008.5416566806], [-5267352.430430681, -1757121.1426313058], [-5267243.970960251, -1757346.3462987433], [-5267228.476845607, -1757635.896994937], [-5267298.200695467, -1757957.6240706423], [-5267530.613639673, -1758190.8789432272], [-5267817.256430407, -1758367.8324056987], [-5268049.669374603, -1758585.0043895384], [-5268227.852694903, -1758922.8314798698], [-5268351.806279989, -1759252.6197547603], [-5268491.254091036, -1759598.5001012173], [-5268560.977940891, -1759880.03433913], [-5268607.460507468, -1760161.5720371422], [-5268708.172809259, -1760451.1569974003], [-5268847.620620306, -1760724.657237898], [-5268971.57420539, -1761102.7363029849], [-5269010.309714644, -1761416.4660740355], [-5269126.516242397, -1761698.0219183285], [-5269196.240092265, -1762051.9826634012], [-5269172.998808974, -1762333.5460661545], [-5269041.298055247, -1762494.441009101], [-5268886.356129559, -1762671.4266172845], [-5268785.643827768, -1762912.7727722619], [-5268739.161261183, -1763162.1664585976], [-5268746.908318514, -1763395.47258374], [-5268739.161261182, -1763580.510144224], [-5268700.4257519385, -1763717.2778917653], [-5268584.2192241745, -1763805.775220264], [-5268491.254091036, -1763849.0182589018], [-5268438.96109231, -1763939.5272407196], [-5268433.150771492, -1764011.9347911892], [-5268456.392054776, -1764066.2405128514], [-5268514.495374316, -1764114.5123701284], [-5268560.977940891, -1764174.8524459284], [-5268537.736657609, -1764235.1925601093], [-5268537.736657609, -1764283.4647623268], [-5268572.598582538, -1764319.6690942799], [-5268560.977940891, -1764380.0095780443], [-5268491.254091036, -1764422.2480083206], [-5268438.96109231, -1764464.4865140626], [-5268409.909488199, -1764561.0320690218], [-5268409.909488199, -1764651.5438921414], [-5268479.633338058, -1764754.1242462846], [-5268531.926336781, -1764802.397508157], [-5268578.408903357, -1764868.7735198294], [-5268578.408903357, -1764959.28640577], [-5268572.598582538, -1765079.9709085121], [-5268520.305695134, -1765200.65602755], [-5268462.202375602, -1765315.3073517042], [-5268404.099167382, -1765478.2339901545], [-5268398.288846554, -1765574.7836864647], [-5268444.771413129, -1765731.677712255], [-5268502.874732671, -1765846.3316132247], [-5268508.6850534985, -1765948.9172698122], [-5268508.685053499, -1766045.4688897924], [-5268468.012696421, -1766142.020788826], [-5268427.340450666, -1766238.573198164], [-5268404.099167382, -1766304.9531290606], [-5268363.426921624, -1766371.3332465393], [-5268351.806279989, -1766431.6790853904], [-5268375.04756327, -1766479.955775011], [-5268404.099167382, -1766510.128756142], [-5268438.96109231, -1766534.2671688097], [-5268456.392054773, -1766570.474949682], [-5268456.392054776, -1766630.8211818116], [-5268427.340450666, -1766697.2022152985], [-5268415.719809027, -1766751.5142243821], [-5268444.771413129, -1766799.7915678993], [-5268491.254091036, -1766866.173076427], [-5268491.254091036, -1766956.6937317073], [-5268485.443658886, -1767071.3535801957], [-5268473.82301724, -1767167.9097896423], [-5268421.530129845, -1767300.675179142], [-5268409.909488199, -1767403.2670385963], [-5268456.392054776, -1767493.7897534668], [-5268543.546978427, -1767566.208082843], [-5268607.460507467, -1767656.7314226655], [-5268677.184468656, -1767717.0804316895], [-5268746.908318514, -1767729.1502520174], [-5268834.063242165, -1767705.0106175353], [-5268903.787092024, -1767674.8361091597], [-5268944.459337781, -1767705.0106175353], [-5268973.510941892, -1767789.4995618674], [-5268979.321262711, -1767886.058593645], [-5268967.700621065, -1768000.7230726958], [-5268892.166450388, -1768067.107958835], [-5268770.149601797, -1768091.247963733], [-5268717.856714403, -1768067.107958835], [-5268653.943074044, -1768049.0029713656], [-5268578.408903357, -1768073.1429577426], [-5268543.546978427, -1768121.4230045967], [-5268537.736657609, -1768205.9134399504], [-5268566.788261712, -1768278.3339548276], [-5268572.598582538, -1768332.6496024926], [-5268566.788261712, -1768386.9652595846], [-5268543.546978427, -1768435.2459486541], [-5268543.546978428, -1768489.5619576066], [-5268555.167620063, -1768531.8077388662], [-5268549.357299247, -1768598.194119433], [-5268508.685053499, -1768640.4400952582], [-5268427.340450666, -1768664.5808024644], [-5268345.995959161, -1768658.5456522552], [-5268293.702960437, -1768640.4400952582], [-5268241.410073043, -1768640.4400952582], [-5268183.306864821, -1768652.5103879634], [-5268136.824186926, -1768664.5808024644], [-5268038.048732956, -1768658.5456522552], [-5267933.46284685, -1768622.3346677844], [-5267852.118355335, -1768592.158986209], [-5267776.584073329, -1768580.0887243862], [-5267677.808619361, -1768622.3346677844], [-5267561.6020915955, -1768706.8268971904], [-5267497.688562556, -1768803.3896835544], [-5267427.964601379, -1768978.4108571478], [-5267364.05107233, -1769105.1511704272], [-5267300.137431969, -1769213.7862833377], [-5267224.603261281, -1769310.3511455143], [-5267050.2935253065, -1769431.0578950134], [-5266951.517960019, -1769497.4468070434], [-5266899.225072614, -1769581.9421719916], [-5266893.414751797, -1769720.7565689438], [-5266928.276676727, -1769823.3589544152], [-5266928.276676727, -1769950.1038098375], [-5266968.948922482, -1770107.0269285238], [-5267015.431600379, -1770378.627018553], [-5267015.431600379, -1770553.6598874202], [-5266945.70763919, -1770680.4086704932], [-5266881.79411015, -1770831.3009459053], [-5266881.79411015, -1771006.337177628], [-5266905.035393433, -1771108.945276296], [-5267021.241921195, -1771211.553706273], [-5267108.396733529, -1771308.1268702021], [-5267154.879411424, -1771428.843737393], [-5267154.879411424, -1771567.6690152136], [-5267154.879411424, -1771688.3873283728], [-5267183.931015534, -1771821.1781760426], [-5267253.654865392, -1771917.753724233], [-5267381.482034793, -1772002.2577694836], [-5267491.878241738, -1772086.7620023687], [-5267579.033054061, -1772171.2666542027], [-5267602.274337354, -1772279.915789292], [-5267660.377656895, -1772352.3489402304], [-5267718.480865116, -1772424.782198409], [-5267730.101506753, -1772497.2156794572], [-5267712.670544288, -1772593.7941165771], [-5267613.894978999, -1772684.336768489], [-5267503.498883385, -1772786.9520640045], [-5267462.826526308, -1772865.4230016358], [-5267486.06792091, -1772986.1478540567], [-5267462.826526308, -1773179.3090218215], [-5267398.912997268, -1773300.0356002685], [-5267387.292355621, -1773438.8719139243], [-5267427.964601379, -1773565.636230249], [-5267474.4471679535, -1773656.1825060719], [-5267491.878241738, -1773782.9479933926], [-5267422.154280552, -1773879.5315922406], [-5267317.568505762, -1774000.2617641701], [-5267282.706469502, -1774163.2484388358], [-5267224.603261282, -1774253.7971308997], [-5267090.965771056, -1774308.1264440943], [-5267015.431600379, -1774356.4192723234], [-5266963.138601655, -1774434.89544535], [-5266951.517960019, -1774495.2618215568], [-5266986.379996267, -1774561.6650143792], [-5266951.517960019, -1774628.068510392], [-5266939.897318372, -1774706.54547442], [-5266968.948922482, -1774778.9860970657], [-5266928.276676727, -1774857.4635648485], [-5266916.656035091, -1774929.904536857], [-5266957.328280838, -1775026.4929624456], [-5266974.759243301, -1775147.229023633], [-5266968.948922482, -1775267.9655893145], [-5266992.190317085, -1775364.555404074], [-5267038.672883663, -1775406.8135222585], [-5267073.5348086, -1775485.293201842], [-5267154.879411424, -1775533.588448361], [-5267242.034223757, -1775533.588448361], [-5267334.999468218, -1775587.9207193283], [-5267422.154280552, -1775666.4010037193], [-5267457.016205491, -1775829.3992304], [-5267462.826526308, -1775925.9912371563], [-5267480.257488772, -1776010.5096844842], [-5267579.033054062, -1776046.7319196914], [-5267648.75701525, -1776095.0283201798], [-5267671.998298531, -1776137.2877520449], [-5267671.998298531, -1776191.6215346763], [-5267671.998298531, -1776282.1779255283], [-5267671.998298531, -1776372.7347811435], [-5267724.291185935, -1776469.3290218834], [-5267840.497713688, -1776571.9609535884], [-5267962.514450951, -1776668.5560136312], [-5268078.720978703, -1776753.0771327454], [-5268194.927506467, -1776916.0828997833], [-5268223.979110579, -1777024.7539626323], [-5268223.979110579, -1777133.425644059], [-5268229.789431398, -1777242.0978284483], [-5268253.03071469, -1777332.6582699404], [-5268351.806279989, -1777386.9948181696], [-5268421.530129845, -1777471.5184076868], [-5268497.0644118525, -1777604.3419217241], [-5268555.167620063, -1777719.0536432993], [-5268636.512111578, -1777779.4285744417], [-5268694.61543112, -1777845.8410508228], [-5268729.477356049, -1777978.6666830063], [-5268729.477356049, -1778105.4554929556], [-5268816.632168382, -1778274.5082665468], [-5268915.4077336695, -1778395.2609106842], [-5268915.407733671, -1778509.9766138892], [-5268892.166450388, -1778648.8437174999], [-5268892.166450388, -1778860.1647397599], [-5268909.597412852, -1779059.4120252193], [-5268973.510941892, -1779210.358093247], [-5269072.286507182, -1779343.191450576], [-5269176.872393298, -1779373.380928605], [-5269287.268488915, -1779373.380928605], [-5269386.044054203, -1779373.380928605], [-5269484.819508181, -1779427.7220869882], [-5269560.353790187, -1779500.1772763762], [-5269560.353790187, -1779614.8982675937], [-5269525.491865258, -1779681.3160557498], [-5269479.009187363, -1779759.8099112383], [-5269490.629828999, -1779898.68441496], [-5269601.026035936, -1780007.3694081113], [-5269676.56031794, -1780079.8262729982], [-5269746.2841678085, -1780170.3976690376], [-5269781.14609274, -1780309.2746049599], [-5269775.33577192, -1780472.3050812427], [-5269757.904809445, -1780562.8781094619], [-5269781.146092739, -1780713.8338572113], [-5269804.3873760225, -1780840.637339572], [-5269874.111337209, -1780931.2117913119], [-5269978.697112006, -1780967.441623767], [-5270060.041714831, -1781027.824802333], [-5270129.7655646885, -1781130.4766786962], [-5270147.196527164, -1781245.2057650005], [-5270170.437921766, -1781281.4360829657], [-5270222.730809169, -1781275.3976927495], [-5270292.454659028, -1781257.2825314235], [-5270356.368299386, -1781281.4360829657], [-5270391.230224317, -1781335.7816649722], [-5270402.850865964, -1781486.7423921318], [-5270443.52311172, -1781583.3578096975], [-5270484.195357477, -1781637.704092123], [-5270484.195357477, -1781746.3970352286], [-5270519.057393726, -1781891.3218596869], [-5270588.781243584, -1781987.9389460918], [-5270710.798092165, -1782036.2475809187], [-5270838.625150255, -1782084.5563153955], [-5270890.918148968, -1782271.753718479], [-5270879.297507323, -1782362.333704021], [-5270919.969753078, -1782458.9527337952], [-5271001.314244584, -1782537.4558950446], [-5271059.417564125, -1782603.8818524817], [-5271117.520772349, -1782694.463122938], [-5271123.331093164, -1782785.0446281873], [-5271146.572376449, -1782899.7818203524], [-5271198.865375172, -1782954.1311581242], [-5271274.39954586, -1783014.519574922], [-5271297.640829141, -1783111.141180713], [-5271349.933827855, -1783165.4911249545], [-5271437.088640188, -1783201.7244141076], [-5271506.812490055, -1783286.2689735915], [-5271547.484847132, -1783419.1254426949], [-5271570.726130415, -1783497.6318242343], [-5271640.449980273, -1783576.1385848522], [-5271646.260301101, -1783739.1918047008], [-5271599.7777345255, -1783811.6602157021], [-5271506.812490055, -1783938.4805907842], [-5271506.812490055, -1784041.1452295007], [-5271535.864205477, -1784155.8884648571], [-5271599.7777345255, -1784270.632378736], [-5271663.691263567, -1784288.749872016], [-5271739.225545572, -1784246.475742874], [-5271832.190678723, -1784210.2408358515], [-5271925.155923194, -1784180.0450071706], [-5272029.74180931, -1784210.2408358515], [-5272093.655338353, -1784306.867379318], [-5272180.810150675, -1784343.1024360235], [-5272273.775395156, -1784373.2984751977], [-5272395.792243726, -1784379.3376645662], [-5272471.326414414, -1784409.533634817], [-5272482.947056058, -1784488.0433399063], [-5272436.464489483, -1784560.5141865313], [-5272401.602564553, -1784608.8281318874], [-5272395.792243726, -1784675.2599697476], [-5272448.085131128, -1784741.6921120097], [-5272541.0503756, -1784777.9278424], [-5272616.584546287, -1784790.006431678], [-5272686.308396146, -1784759.809970187], [-5272738.601394869, -1784717.5348738972], [-5272843.187169657, -1784693.3777764556], [-5272930.3420933075, -1784741.6921120097], [-5272965.204018245, -1784820.2029321608], [-5272947.773055772, -1784934.95010532], [-5272901.290489195, -1785007.4222212117], [-5272878.049205914, -1785091.9734226873], [-5272895.480168377, -1785146.327853565], [-5272901.290489195, -1785200.6824108162], [-5272860.61824344, -1785267.1160458657], [-5272785.083961434, -1785327.5103181882], [-5272814.135565545, -1785412.06256155], [-5272825.75620719, -1785472.4573240506], [-5272860.618243441, -1785532.852126859], [-5272924.531772491, -1785563.049586782], [-5272936.152414135, -1785677.800405982], [-5272912.911130844, -1785762.3539164164], [-5272883.859526733, -1785871.0661378547], [-5272895.480168378, -1785955.6204631743], [-5272965.204018248, -1786040.1749787033], [-5273029.117658598, -1786130.7695576875], [-5273093.031187647, -1786197.2057206563], [-5273139.51375421, -1786257.6023963485], [-5273133.703433394, -1786330.0787289527], [-5273087.220866819, -1786372.3566259623], [-5273069.789904354, -1786438.793475819], [-5273069.789904354, -1786547.5088442431], [-5273162.755037505, -1786674.3440608543], [-5273220.858357048, -1786752.861373178], [-5273232.478998692, -1786831.3790651055], [-5273191.806641615, -1786879.6977047252], [-5273145.32407504, -1786897.8172203493], [-5273116.27247093, -1786946.1359974113], [-5273133.703433394, -1787012.5745947703], [-5273220.858357048, -1787048.8138462408], [-5273296.392527733, -1787048.8138462408], [-5273360.306056772, -1787091.093044006], [-5273360.306056772, -1787145.4521250348], [-5273319.6338110175, -1787193.771530097], [-5273220.858357048, -1787199.8114482919], [-5273145.32407504, -1787217.9312122532], [-5273145.32407504, -1787302.4902966397], [-5273162.755037505, -1787344.7700693558], [-5273151.134395866, -1787417.2496594558], [-5273116.27247093, -1787477.649489714], [-5273081.410546, -1787544.0895991584], [-5273069.789904354, -1787622.60983546], [-5273087.220866819, -1787725.290658629], [-5273151.134395866, -1787803.8115043067], [-5273215.048036219, -1787834.0118998825], [-5273255.720281975, -1787882.3327297836], [-5273255.720281975, -1787948.7738750777], [-5273215.048036218, -1788039.3757415726], [-5273197.617073764, -1788136.0182358222], [-5273203.427394581, -1788280.982669527], [-5273215.048036218, -1788383.6662728146], [-5273273.151244439, -1788498.4309503462], [-5273371.926809729, -1788601.1156260546], [-5273459.081622062, -1788685.6797205764], [-5273522.99515111, -1788740.0425145826], [-5273552.046866532, -1788800.4458832995], [-5273610.150074754, -1788854.808944679], [-5273633.391358045, -1788872.92999327], [-5273662.442962148, -1788933.3337059782], [-5273615.960395572, -1788993.7374593294], [-5273557.85718735, -1789054.1413690525], [-5273517.184830285, -1789120.5859660935], [-5273453.271301243, -1789205.1519432582], [-5273441.650659597, -1789295.7588032568], [-5273459.081622062, -1789386.365899435], [-5273522.99515111, -1789464.8924498537], [-5273586.908791462, -1789513.216541044], [-5273621.770716401, -1789585.7028655587], [-5273615.960395572, -1789682.35176438], [-5273575.288149825, -1789736.716880856], [-5273505.564188637, -1789785.0415352236], [-5273511.374509456, -1789881.6912600861], [-5273534.615904069, -1789930.0162148378], [-5273522.99515111, -1790020.6258906543], [-5273511.374509456, -1790105.1951310455], [-5273546.2365457155, -1790183.7241017497], [-5273604.339753936, -1790238.0903870512], [-5273662.442962148, -1790304.5382412209], [-5273662.442962148, -1790364.9456614282], [-5273662.442962148, -1790413.2716176983], [-5273691.494566259, -1790467.6384382392], [-5273737.977244154, -1790485.7607399216], [-5273749.5978858, -1790558.2502032565], [-5273749.5978858, -1790642.8213936798], [-5273778.649489911, -1790715.3113453868], [-5273865.804302244, -1790787.8014067865], [-5273952.959225886, -1790866.332676759], [-5274011.062434107, -1790920.7005539062], [-5274028.493396574, -1791017.354986589], [-5274022.683075756, -1791077.7641382224], [-5274063.355432821, -1791168.3781592436], [-5274109.837999406, -1791198.5830269482], [-5274173.751528438, -1791210.664938698], [-5274237.665168806, -1791234.8287809896], [-5274255.096131271, -1791277.11556527], [-5274226.04452716, -1791307.3204581707], [-5274179.561849264, -1791337.5253902203], [-5274179.561849264, -1791410.017502601], [-5274214.423885514, -1791482.5097247704], [-5274214.423885515, -1791561.0433356857], [-5274179.561849264, -1791603.3307119582], [-5274127.268961872, -1791639.5770955533], [-5274092.407036933, -1791712.0701476478], [-5274092.407036933, -1791820.8099751105], [-5274051.734791185, -1791935.5915704516], [-5274063.355432821, -1792044.3324412203], [-5274045.924470356, -1792080.5796266827], [-5274005.252113289, -1792104.7443711692], [-5273952.959225886, -1792159.115137907], [-5273912.286980138, -1792249.733480245], [-5273935.528263423, -1792328.2695617983], [-5273958.769546704, -1792394.7233761013], [-5273947.148905068, -1792473.2600623001], [-5273894.856017675, -1792527.6316892912], [-5273871.614734381, -1792588.0448728], [-5273825.132056489, -1792660.540760981], [-5273819.321735668, -1792763.2437713346], [-5273842.563018952, -1792835.7402049485], [-5273865.8043022435, -1792908.236864284], [-5273836.752698132, -1792992.8167012013], [-5273761.2185274465, -1793010.9409671486], [-5273668.253282976, -1793010.9409671503], [-5273592.71911229, -1793016.98239226], [-5273563.667508179, -1793065.3138496159], [-5273569.477829006, -1793143.8527975532], [-5273598.529433118, -1793270.7237784655], [-5273633.391358045, -1793397.5955665736], [-5273662.442962149, -1793518.426489337], [-5273668.253282976, -1793590.9252746268], [-5273668.253282976, -1793663.424401475], [-5273714.735960871, -1793735.9236384], [-5273726.356602519, -1793790.2982143073], [-5273679.873924622, -1793844.6730329958], [-5273569.477829006, -1793862.7979622162], [-5273488.133226174, -1793844.6730329974], [-5273430.030017952, -1793874.881256192], [-5273366.116488911, -1793971.54783392], [-5273244.099640329, -1794025.9230761083], [-5273063.979583536, -1794056.131534585], [-5272930.3420933075, -1794080.298329592], [-5272808.325244726, -1794140.715426937], [-5272773.463319796, -1794219.2580037566], [-5272808.325244726, -1794309.8842498215], [-5272878.049205914, -1794400.5109646257], [-5272941.962734954, -1794442.8034983303], [-5272953.583376592, -1794479.0543026912], [-5272895.480168377, -1794533.4307308972], [-5272854.807811301, -1794569.6816764746], [-5272872.238885085, -1794654.2674358352], [-5272918.7214516625, -1794738.8536185257], [-5273005.876263995, -1794799.2724260755], [-5273011.6865848135, -1794889.9010474104], [-5273011.6865848135, -1794998.6557202158], [-5273063.979583536, -1795095.3270831616], [-5273104.651829283, -1795143.6628574403], [-5273116.27247093, -1795222.2088206618], [-5273098.841508465, -1795282.6288838498], [-5273029.117658598, -1795361.175200679], [-5272907.100810024, -1795373.2592729614], [-5272796.70460308, -1795349.091134667], [-5272686.308396146, -1795349.091134667], [-5272610.7742254585, -1795385.3434673033], [-5272517.808980989, -1795475.9742567409], [-5272419.0335270185, -1795572.647603933], [-5272389.981922909, -1795663.2791234504], [-5272326.068282549, -1795784.1218147893], [-5272273.775395154, -1795892.8806698555], [-5272262.154753509, -1796031.8511742055], [-5272273.775395153, -1796152.6957814496], [-5272297.016678437, -1796297.7101163038], [-5272395.792243726, -1796388.3444626904], [-5272448.085131128, -1796460.8523100365], [-5272488.757376886, -1796551.4872926415], [-5272552.671017237, -1796636.0803777522], [-5272610.7742254585, -1796696.5041158218], [-5272721.170432403, -1796750.8856144245], [-5272773.463319798, -1796847.5642641517], [-5272814.135565545, -1796938.200755086], [-5272924.531772491, -1797010.710318161], [-5273011.6865848135, -1797028.837715355], [-5273093.031187647, -1797107.3899332664], [-5273145.32407504, -1797167.8150127633], [-5273244.099640329, -1797167.8150127633], [-5273371.926809729, -1797149.687507142], [-5273499.753867819, -1797173.8575177884], [-5273604.339753936, -1797276.5803435473], [-5273662.442962148, -1797379.303739317], [-5273656.6326413285, -1797439.7294103347], [-5273645.011999684, -1797506.1979457636], [-5273586.908791462, -1797530.368327353], [-5273505.564188637, -1797475.9848883837], [-5273430.0300179515, -1797367.2186239732], [-5273366.116488911, -1797294.7079481855], [-5273290.582206907, -1797264.495281643], [-5273244.099640329, -1797300.750486214], [-5273267.340923623, -1797397.4314241046], [-5273313.823490197, -1797512.2405388071], [-5273354.495735945, -1797566.6239468853], [-5273435.840338778, -1797596.837006398], [-5273511.374509456, -1797645.1779833601], [-5273522.995151111, -1797741.8603548997], [-5273499.753867819, -1797790.2016337132], [-5273389.357772204, -1797784.158968362], [-5273331.254452662, -1797772.0736423673], [-5273238.2893195115, -1797790.2016337132], [-5273238.2893195115, -1797856.6711721483], [-5273273.151244439, -1797947.311600568], [-5273319.6338110175, -1798007.738865166], [-5273308.013169379, -1798080.2516513134], [-5273249.909961157, -1798122.5508811423], [-5273267.340923623, -1798219.2352401551], [-5273267.340923623, -1798321.962689722], [-5273290.582206907, -1798388.4337503586], [-5273354.495735945, -1798442.819210442], [-5273406.788734667, -1798503.2476488305], [-5273412.599055486, -1798563.6763602975], [-5273383.547451385, -1798612.0193501], [-5273360.306056772, -1798666.405333973], [-5273366.116488911, -1798732.877380845], [-5273400.978413842, -1798769.1348780345], [-5273395.168093022, -1798823.5212300185], [-5273348.685415127, -1798883.950775219], [-5273308.01316938, -1798962.5092686897], [-5273308.013169379, -1799053.154130417], [-5273360.306056772, -1799149.842249644], [-5273435.840338778, -1799222.3586034107], [-5273488.133226174, -1799270.7030809277], [-5273511.374509456, -1799313.004480066], [-5273517.184830285, -1799367.392106546], [-5273482.322905353, -1799421.7799762904], [-5273459.081622062, -1799488.254070909], [-5273447.460980426, -1799530.555866634], [-5273464.891942889, -1799590.9872529572], [-5273546.236545714, -1799621.2029472513], [-5273633.391358045, -1799609.1166648092], [-5273749.5978858, -1799627.2460908396], [-5273813.511414841, -1799681.634453893], [-5273819.321735668, -1799784.3684922038], [-5273772.839169083, -1799838.7572236077], [-5273726.356602519, -1799868.9732405955], [-5273656.6326413285, -1799875.0164487108], [-5273592.71911229, -1799887.1028696648], [-5273557.85718735, -1799941.491957707], [-5273563.667508179, -1800032.1405281816], [-5273604.339753936, -1800062.356797057], [-5273674.063603794, -1800068.4000555526], [-5273726.356602519, -1800062.356797057], [-5273784.459810738, -1800098.6163716607], [-5273830.942377305, -1800159.0492377465], [-5273883.235376027, -1800207.3955513528], [-5273958.769546704, -1800237.6120485307], [-5274045.924470358, -1800249.6986584156], [-5274127.268961872, -1800255.7419657307], [-5274167.941207619, -1800310.1319181425], [-5274156.320565983, -1800352.435212599], [-5274115.648320225, -1800388.6952406813], [-5274057.545112004, -1800400.7819293102], [-5274022.683075756, -1800455.1722218592], [-5274034.303717401, -1800545.8227999443], [-5274005.252113289, -1800624.3869209131], [-5273964.579867533, -1800678.7777379006], [-5273912.286980138, -1800708.994849252], [-5273854.1836605985, -1800757.3423093164], [-5273813.511414841, -1800817.7768919445], [-5273836.752698132, -1800854.0376476524], [-5273889.045696845, -1800860.0811124477], [-5273929.717942604, -1800902.385410142], [-5273941.338584248, -1800980.9508549988], [-5273941.338584249, -1801035.342392623], [-5273958.769546704, -1801077.6470100947], [-5274034.303717401, -1801077.6470100947], [-5274098.21735776, -1801047.429418305], [-5274150.510245153, -1801005.1248559703], [-5274220.234206332, -1800993.0378523236], [-5274255.096131271, -1801035.342392623], [-5274260.9064520905, -1801101.8211118788], [-5274237.665168806, -1801192.474333882], [-5274191.182490911, -1801240.8228021413], [-5274138.889603509, -1801252.9099349643], [-5274080.786395294, -1801264.997074089], [-5274011.062434107, -1801289.1713712367], [-5273993.631471644, -1801355.6509341805], [-5274011.062434107, -1801434.2177993543], [-5274086.596716114, -1801494.6540306995], [-5274185.37217008, -1801561.134182971], [-5274237.665168806, -1801603.439760006], [-5274266.716772918, -1801651.7890854753], [-5274260.9064520905, -1801712.225999918], [-5274202.803132549, -1801730.3570702565], [-5274162.130886801, -1801700.138627586], [-5274127.268961872, -1801700.138627586], [-5274109.837999406, -1801730.3570702565], [-5274086.596716114, -1801796.8377828132], [-5274086.596716114, -1801869.36252982], [-5274115.648320225, -1801935.8436411482], [-5274167.941207618, -1801978.1499022082], [-5274214.423885514, -1802008.368707395], [-5274260.9064520905, -1802056.718993452], [-5274260.9064520905, -1802111.1130555868], [-5274220.234206332, -1802177.5948605048], [-5274127.268961872, -1802183.6386704056], [-5274016.872754927, -1802183.6386704056], [-5273947.148905069, -1802183.6386704056], [-5273894.856017675, -1802219.9016787298], [-5273877.4250552, -1802304.5153153567], [-5273859.993981415, -1802383.0855132823], [-5273819.321735669, -1802419.3487179894], [-5273778.649489911, -1802401.217108538], [-5273749.5978858, -1802340.7783970854], [-5273691.494566259, -1802322.6468491217], [-5273645.011999684, -1802346.8222495525], [-5273627.581037219, -1802401.217108538], [-5273639.201678865, -1802467.699745875], [-5273697.304887087, -1802503.9630830267], [-5273761.2185274465, -1802522.0947728828], [-5273842.563018952, -1802528.138672651], [-5273935.528263423, -1802558.3583109651], [-5273993.631471644, -1802612.7535508447], [-5273993.631471644, -1802679.2367952566], [-5273947.148905069, -1802727.5884813825], [-5273894.856017675, -1802788.028086218], [-5273877.4250552, -1802848.4678487673], [-5273912.286980138, -1802908.9078848271], [-5273982.010829996, -1802945.1719126946], [-5274028.493396574, -1803011.6561112285], [-5274045.924470356, -1803078.1406164311], [-5274045.924470358, -1803138.5811360495], [-5274034.303717401, -1803174.8455235306], [-5273982.010829996, -1803180.8895936287], [-5273958.769546704, -1803144.6251966825], [-5273941.338584249, -1803096.2727557407], [-5273894.856017675, -1803053.964336947], [-5273859.993981415, -1803078.1406164311], [-5273848.373339778, -1803114.40490927], [-5273871.614734382, -1803199.0218133978], [-5273883.235376027, -1803253.418673693], [-5273859.993981415, -1803301.7714427358], [-5273819.321735668, -1803331.9919746572], [-5273801.890773194, -1803398.4772837101], [-5273772.839169083, -1803477.051192689], [-5273749.5978858, -1803585.846115728], [-5273679.873924622, -1803724.8627104869], [-5273656.6326413285, -1803882.0128946216], [-5273615.960395572, -1804002.8984042911], [-5273517.184830285, -1804057.2970374962], [-5273383.547451385, -1804069.385640005], [-5273313.823490197, -1804123.784545265], [-5273296.392527733, -1804214.4494784456], [-5273302.20284855, -1804329.292352798], [-5273313.823490197, -1804474.3578202403], [-5273313.823490197, -1804619.4240811905], [-5273308.01316938, -1804716.1355428647], [-5273285.657543959, -1804775.179088255], [-5273192.976832101, -1804951.5879943338], [-5273162.083224382, -1804991.761621987], [-5273115.743035426, -1804959.622667958], [-5273077.126081441, -1804919.4492119057], [-5273038.509238763, -1804863.2064211392], [-5272945.828638222, -1804863.2064211392], [-5272829.977887563, -1804863.2064211392], [-5272783.637698618, -1804863.2064211392], [-5272737.297398344, -1804895.3451254342], [-5272721.85059448, -1804967.6574601918], [-5272721.8505944805, -1805072.108970783], [-5272714.127248222, -1805128.3522693904], [-5272683.233751812, -1805208.7000925534], [-5272505.596008341, -1805353.326831194], [-5272405.192061535, -1805417.60576202], [-5272312.511460996, -1805441.7103926546], [-5272243.0010106, -1805401.5359836111], [-5272196.660710338, -1805393.5011565061], [-5272134.873717526, -1805385.4663321786], [-5272103.980221116, -1805433.6754357824], [-5272150.320521391, -1805481.8847556633], [-5272173.490671513, -1805538.1290505782], [-5272173.490671513, -1805586.3385881705], [-5272134.873717526, -1805634.5482262569], [-5272057.639920852, -1805634.5482262569], [-5271934.065823914, -1805650.6181665442], [-5271849.108680972, -1805650.6181665442], [-5271740.981387899, -1805658.6530829796], [-5271617.407290969, -1805618.4782971258], [-5271478.386390166, -1805562.2338068134], [-5271347.088946961, -1805514.0243194653], [-5271161.7278572, -1805514.0243194653], [-5271030.430413996, -1805514.0243194653], [-5270930.0264671985, -1805514.0243194653], [-5270845.069212938, -1805594.3734822746], [-5270713.771769724, -1805722.9327465233], [-5270636.537973059, -1805819.3526926334], [-5270605.644476649, -1805923.808049303], [-5270536.134026253, -1806004.1586358242], [-5270420.283386911, -1806060.4042009201], [-5270281.262486108, -1806084.5095015336], [-5270173.135193034, -1806092.5445687233], [-5270080.454592495, -1806124.6850970031], [-5269964.603953143, -1806197.0011594647], [-5269949.157149288, -1806253.247193653], [-5269918.263652889, -1806357.7045104138], [-5269956.880495566, -1806462.1622991264], [-5269987.773991966, -1806566.6205598083], [-5269987.773991966, -1806630.9028633563], [-5269987.773991967, -1806719.2911923195], [-5270003.22079582, -1806767.5031926946], [-5270010.944142099, -1806847.856711495], [-5270010.944142099, -1806920.175152], [-5269972.327299421, -1806960.3520839592], [-5269864.200006346, -1806928.210509648], [-5269794.689555938, -1806888.0335177435], [-5269725.179105544, -1806831.7859390574], [-5269678.838805279, -1806783.5738046125], [-5269609.328466202, -1806743.3971799323], [-5269570.711512206, -1806743.3971799323], [-5269555.26470834, -1806815.7152936195], [-5269547.541362073, -1806879.9981740743], [-5269539.818015796, -1806952.3167151462], [-5269470.3075653985, -1807032.6708764697], [-5269315.839972061, -1807137.1317154262], [-5269199.9892213885, -1807209.4510611217], [-5269060.968431916, -1807265.6995589642], [-5268983.734635241, -1807289.8061164862], [-5268914.224184845, -1807313.912699164], [-5268852.437192036, -1807386.2324822801], [-5268844.713734449, -1807466.5881523977], [-5268829.267041902, -1807554.9797933616], [-5268767.479937775, -1807627.3003309856], [-5268697.969598688, -1807667.4786082031], [-5268682.5227948325, -1807771.9423170136], [-5268667.075990967, -1807876.4064981844], [-5268597.565651892, -1807940.6923294724], [-5268466.268208685, -1807972.8353121988], [-5268319.5239616055, -1808004.9784554834], [-5268180.503060813, -1808061.2288901075], [-5267941.078324522, -1808181.765963922], [-5267763.440581052, -1808414.8062370454], [-5267608.972987712, -1808583.5610621327], [-5267462.228740642, -1808768.38911309], [-5267307.761147293, -1808824.6414071366], [-5267114.676599959, -1808840.7134501382], [-5266906.14536008, -1808872.8578013568], [-5266705.337466476, -1808993.3991115766], [-5266666.720623798, -1809049.6519538376], [-5266651.273819933, -1809154.1219147644], [-5266651.273819933, -1809346.9907324358], [-5266589.486827125, -1809636.2970384744], [-5266504.529572864, -1810062.2269043047], [-5266411.849083655, -1810464.054619476], [-5266379.6635019155, -1810579.4125172372], [-5278335.199481162, -1810437.4833855156], [-5278502.067509184, -1810437.4833855156], [-5284275.7006552415, -1810437.4833855156], [-5286595.166033188, -1810437.4833855156], [-5290066.020592969, -1810420.1198379423], [-5292969.524013325, -1810385.3927819885], [-5295861.078844817, -1810410.590510811], [-5297690.825960399, -1810426.5353139506], [-5298307.805109926, -1810332.0865053071], [-5298445.542830951, -1810343.1113298417], [-5298699.828051338, -1810332.0865053071], [-5298859.964701069, -1810420.1198379423], [-5299343.881993451, -1810472.210519877], [-5300578.705356265, -1810385.3927819885], [-5300751.449494922, -1810563.2995320146], [-5307033.157153423, -1810587.405941898], [-5307860.586579522, -1810586.8622124079], [-5308094.984127595, -1810586.708151856], [-5308453.025569947, -1810588.0125698156], [-5314269.860029257, -1810609.203898905], [-5322681.969823659, -1810637.2225762717], [-5331086.397571321, -1810662.1592555437], [-5336493.866069158, -1810676.4787136314], [-5336564.793616671, -1810676.666482901], [-5336559.600562432, -1810662.0333424867], [-5341303.116425213, -1810679.406223157], [-5343384.213154885, -1810674.3051773373], [-5345579.684872737, -1810694.1069066986], [-5345684.877004801, -1810694.4814029974], [-5352965.005947963, -1810717.859459507], [-5359541.848738502, -1810712.6857762565], [-5374402.030610051, -1810693.8342298486], [-5374437.634479379, -1810693.7890539865], [-5374437.06663866, -1810632.5958850505], [-5374392.56678353, -1810599.5208963794], [-5374322.638328451, -1810559.8309725712], [-5374265.424228996, -1810460.6063459695], [-5374246.352751199, -1810374.6119993478], [-5374233.638506883, -1810255.5434777208], [-5374252.709873361, -1810149.705294821], [-5374259.066995524, -1810043.867597373], [-5374303.566961962, -1809971.104425793], [-5374322.638328451, -1809885.1119035075], [-5374278.138473322, -1809852.0379863365], [-5374189.138651754, -1809825.5788867488], [-5374176.424407429, -1809792.504939069], [-5374227.281384708, -1809726.357417586], [-5374284.495595485, -1809620.5216617289], [-5374309.924084124, -1809541.1451343596], [-5374271.781351169, -1809481.6128890214], [-5374227.281384708, -1809481.6128890214], [-5374163.710163114, -1809461.7688799677], [-5374125.567318828, -1809382.3928985368], [-5374150.99580747, -1809329.4758064495], [-5374195.495773915, -1809250.1002799296], [-5374176.424407429, -1809177.3397265654], [-5374106.495952348, -1809097.9647231451], [-5374049.281741574, -1808992.1318483846], [-5373972.996275642, -1808939.2155350067], [-5373877.639331922, -1808906.1429731275], [-5373794.996632507, -1808839.9978756618], [-5373763.211021712, -1808780.467554199], [-5373794.996632507, -1808734.1661833376], [-5373839.496487635, -1808668.0216943452], [-5373845.853609789, -1808628.334976066], [-5373814.067998996, -1808588.648441819], [-5373756.853788221, -1808529.1186525095], [-5373686.925444462, -1808476.2035160186], [-5373623.3541115355, -1808416.674016562], [-5373597.925622895, -1808363.7591377005], [-5373629.7112336885, -1808297.6155937905], [-5373674.211200136, -1808231.47235513], [-5373674.211200136, -1808165.3291900707], [-5373648.782600175, -1808079.3433933842], [-5373629.711233688, -1807966.9009470898], [-5373610.639867209, -1807874.3016759683], [-5373559.782889928, -1807854.4590481159], [-5373559.782889928, -1807788.3169625779], [-5373604.282745047, -1807708.9468488619], [-5373578.854256405, -1807642.8051799035], [-5373591.568500722, -1807589.8919579082], [-5373642.425478011, -1807550.207207795], [-5373655.139722337, -1807497.2943136676], [-5373585.211378567, -1807450.995514897], [-5373489.854434838, -1807404.6969247076], [-5373445.3544683885, -1807365.0123768442], [-5373400.854613271, -1807279.029558744], [-5373426.283101912, -1807186.4331602226], [-5373426.283101912, -1807113.6789915585], [-5373432.640224075, -1806988.0133239238], [-5373413.568857585, -1806921.8737184394], [-5373375.426124619, -1806763.139599599], [-5373394.497491108, -1806644.089581046], [-5373426.283101912, -1806564.72339706], [-5373502.568679153, -1806491.9711852847], [-5373553.425656443, -1806445.6744003338], [-5373547.068534283, -1806379.5364623985], [-5373559.782889928, -1806326.6261324668], [-5373604.282745047, -1806260.4885350876], [-5373623.3541115355, -1806181.123552142], [-5373604.282745047, -1806095.1451376928], [-5373597.925622895, -1805923.1892678465], [-5373578.8542564055, -1805843.825558401], [-5373623.354111535, -1805757.8483980275], [-5373597.726917595, -1805703.6995073857], [-5373541.406713668, -1805633.9468240554], [-5373503.859985256, -1805522.3429219844], [-5373407.311032408, -1805366.0984563741], [-5373372.446213171, -1805287.9766193433], [-5373372.446213171, -1805193.114652345], [-5373302.71646338, -1805087.0930573044], [-5373224.940874748, -1804972.7017746065], [-5373131.073942398, -1804908.531316787], [-5373082.7994659785, -1804838.7810310912], [-5373039.888919221, -1804833.201026585], [-5373005.023988666, -1804847.1510403785], [-5372956.749512247, -1804838.7810310912], [-5372933.923895937, -1804774.9690164172], [-5372945.669438053, -1804651.1970813477], [-5372945.669438053, -1804602.3218765485], [-5372939.796666996, -1804516.790313879], [-5372881.068956428, -1804461.8060206098], [-5372863.450643266, -1804419.0404594277], [-5372863.450643266, -1804345.7283684853], [-5372845.832330092, -1804302.9630217936], [-5372769.486417681, -1804278.5257163078], [-5372687.267734213, -1804260.19775413], [-5372628.540023653, -1804180.7768681652], [-5372563.939653349, -1804089.13770518], [-5372605.048939423, -1803948.6249648004], [-5372640.285565761, -1803875.3142479858], [-5372681.394963164, -1803820.331478524], [-5372587.430737579, -1803740.9119859107], [-5372534.57579807, -1803710.366215549], [-5372511.084713848, -1803649.2745641507], [-5372469.975316445, -1803606.5105040788], [-5372440.611572485, -1803557.6375050854], [-5372452.3571146, -1803496.5462566717], [-5372534.57579807, -1803447.6734897867], [-5372546.321340184, -1803392.691620066], [-5372558.066882301, -1803301.055576444], [-5372522.8302559545, -1803178.8747104213], [-5372516.957484898, -1803117.7844612477], [-5372469.975316445, -1803038.367494027], [-5372428.866030381, -1802995.6045621196], [-5372340.774464534, -1802958.9506833197], [-5372240.937467891, -1802934.5149121534], [-5372164.59155548, -1802885.6432155573], [-5372152.846013363, -1802830.6626801437], [-5372099.991073864, -1802775.6823910447], [-5372099.991073864, -1802726.8110295725], [-5372170.464326538, -1802647.3954028927], [-5372141.100471258, -1802580.1976795963], [-5372099.991073863, -1802537.4357093414], [-5372053.009016732, -1802476.347151892], [-5372111.736615971, -1802403.0412113827], [-5372199.828181815, -1802403.0412113827], [-5372270.301323168, -1802403.0412113827], [-5372317.283491632, -1802372.4970897068], [-5372317.283491632, -1802280.8650821045], [-5372287.919636344, -1802201.4508682839], [-5372217.44638367, -1802201.4508682839], [-5372158.718784421, -1802183.124549911], [-5372129.354929143, -1802140.3633125187], [-5372105.863844922, -1802085.3845458047], [-5372047.136245674, -1801999.8623948821], [-5372017.772390395, -1801944.8839615209], [-5372111.736615971, -1801902.1230479882], [-5372164.59155548, -1801896.0143524932], [-5372182.209868654, -1801816.6015733855], [-5372193.955410768, -1801767.7322348715], [-5372217.44638367, -1801669.9939827672], [-5372146.973242316, -1801627.2336921976], [-5372082.372872012, -1801615.0164477278], [-5372058.88178779, -1801535.604515677], [-5372105.863844923, -1801443.9758171528], [-5372141.100471258, -1801370.6730265035], [-5372193.955410768, -1801279.0449799572], [-5372235.0646968335, -1801205.7428266273], [-5372276.174094228, -1801150.7662769067], [-5372317.283491632, -1801089.6814903412], [-5372287.919636343, -1801010.2713579023], [-5372287.919636341, -1800943.0784971428], [-5372282.046865286, -1800863.668866811], [-5372240.937467891, -1800820.9100635413], [-5372188.082639711, -1800759.826030224], [-5372123.482158086, -1800729.2840739146], [-5372058.88178779, -1800680.4171432701], [-5372011.899619336, -1800631.5501998214], [-5371982.535764058, -1800594.9000598148], [-5371988.408535117, -1800533.8167376432], [-5371941.426477984, -1800466.6251417077], [-5371894.444309533, -1800442.1919981507], [-5371818.098397119, -1800442.1919981507], [-5371806.352855005, -1800399.4338562365], [-5371835.716598975, -1800350.5675048588], [-5371876.825996358, -1800326.1343677759], [-5371947.299249041, -1800307.809647648], [-5372000.154077223, -1800277.268286629], [-5371970.790221943, -1800210.077433937], [-5371894.444309533, -1800210.077433937], [-5371835.716598975, -1800246.7269658165], [-5371782.861770785, -1800277.268286629], [-5371730.006831275, -1800295.59309841], [-5371730.006831275, -1800228.402192643], [-5371735.879602333, -1800161.211597292], [-5371765.243457613, -1800112.3457478087], [-5371823.971168177, -1800094.0210807992], [-5371829.843939236, -1800032.9390777461], [-5371759.370686552, -1800002.3980786498], [-5371706.515747054, -1799984.0734984835], [-5371665.406460968, -1799965.7489328058], [-5371630.169834642, -1799941.3162010827], [-5371630.169834642, -1799874.1264372908], [-5371636.0426057, -1799819.153044213], [-5371647.788147806, -1799764.1798971714], [-5371618.424292526, -1799715.314884018], [-5371577.315006452, -1799654.233878083], [-5371553.823922221, -1799605.3690964926], [-5371477.477898492, -1799587.0448299346], [-5371430.495841358, -1799587.0448299346], [-5371389.386443973, -1799587.0448299346], [-5371330.658733405, -1799580.9367443034], [-5371318.913302611, -1799507.6399578322], [-5371318.913302611, -1799446.559382839], [-5371342.404275523, -1799330.5068490647], [-5371342.4042755235, -1799263.318854071], [-5371342.4042755235, -1799214.454895688], [-5371342.404275521, -1799147.2671209134], [-5371342.404275521, -1799092.2955656366], [-5371377.640901857, -1798964.0289553166], [-5371371.7681308, -1798896.8419055021], [-5371383.513672915, -1798823.5472796503], [-5371389.386443974, -1798756.3606364627], [-5371424.6230703015, -1798707.4978611595], [-5371489.223440607, -1798707.4978611595], [-5371547.951151162, -1798676.9586064692], [-5371547.951151162, -1798573.125556804], [-5371536.205609058, -1798493.7240377744], [-5371489.223440607, -1798487.6162398674], [-5371448.114154532, -1798518.1552454806], [-5371401.13198609, -1798560.9099208233], [-5371354.149817626, -1798615.8803333845], [-5371313.040531552, -1798664.7429158378], [-5371242.567278878, -1798695.2821544681], [-5371189.712450689, -1798701.3900070041], [-5371166.221366467, -1798646.4193919562], [-5371172.094137526, -1798542.5864788874], [-5371219.07619466, -1798408.2151290677], [-5371242.56727888, -1798304.3832813695], [-5371224.948965716, -1798237.1981406673], [-5371266.05836311, -1798194.4441772164], [-5371324.786073668, -1798206.6596203928], [-5371377.640901857, -1798261.6290784334], [-5371395.259215032, -1798310.491031075], [-5371436.368612416, -1798310.491031075], [-5371453.98681427, -1798249.4136063375], [-5371471.605127444, -1798188.3364580474], [-5371471.605127444, -1798115.0439532301], [-5371442.241383473, -1798023.4287633079], [-5371442.241383473, -1797895.1680583365], [-5371436.368612416, -1797827.9842173913], [-5371418.750299252, -1797724.1548483814], [-5371383.513672917, -1797589.7882344543], [-5371336.531504463, -1797522.6052768647], [-5371307.167760495, -1797449.3148730104], [-5371342.404275523, -1797357.7023091558], [-5371377.640901857, -1797296.6275058878], [-5371424.6230703015, -1797278.3050614845], [-5371500.96898272, -1797302.7349905663], [-5371553.823922221, -1797345.4873125013], [-5371630.169834642, -1797388.2398288737], [-5371706.515747054, -1797363.809809882], [-5371724.134060217, -1797314.9498489716], [-5371730.006831276, -1797223.3378150065], [-5371730.006831275, -1797113.403827974], [-5371765.243457613, -1796997.3629551183], [-5371776.988999726, -1796930.1815950323], [-5371776.988999726, -1796856.893175881], [-5371853.334912139, -1796832.4637156022], [-5371953.172020099, -1796832.4637156022], [-5372035.390703559, -1796844.6784425317], [-5372099.991073864, -1796783.6048721115], [-5372105.863844922, -1796673.6729656444], [-5372105.863844923, -1796606.4926571737], [-5372099.991073864, -1796551.5269999728], [-5372158.718784421, -1796490.4543157658], [-5372258.555781064, -1796533.2051431262], [-5372329.029033735, -1796466.025240721], [-5372376.011090869, -1796404.9526654352], [-5372387.756632986, -1796337.773133754], [-5372446.484343543, -1796343.8803664106], [-5372528.70302701, -1796362.2020739918], [-5372581.557966522, -1796404.9526654352], [-5372640.285565761, -1796404.9526654352], [-5372693.140505269, -1796404.9526654352], [-5372757.7408755645, -1796435.4889330117], [-5372792.977501911, -1796533.2051431262], [-5372798.85027297, -1796606.4926571737], [-5372828.214128248, -1796691.994933228], [-5372851.70510115, -1796753.0682628693], [-5372951.542209111, -1796850.7858084023], [-5373010.269808348, -1796887.4300373243], [-5373074.870289974, -1796911.8595553928], [-5373139.470660271, -1796942.3963733204], [-5373227.562114796, -1796936.2889833795], [-5373327.399222749, -1796832.4637156022], [-5373397.872364113, -1796801.926926385], [-5373444.854532554, -1796795.819573355], [-5373503.582131802, -1796789.7122219289], [-5373568.1826134175, -1796789.7122219289], [-5373656.274067943, -1796759.1756046512], [-5373732.619980355, -1796722.5315779892], [-5373785.474919865, -1796710.3169152702], [-5373838.329859375, -1796710.3169152702], [-5373902.93022967, -1796710.3169152702], [-5373944.039627066, -1796746.9609226803], [-5373985.14891314, -1796771.3902930538], [-5374043.876623697, -1796777.4976396642], [-5374067.367707928, -1796710.3169152702], [-5374043.876623697, -1796624.8145717902], [-5374026.258310534, -1796582.0634601405], [-5373973.403482343, -1796520.9905799192], [-5373944.039627066, -1796459.9179759691], [-5373914.675771777, -1796417.1671676545], [-5373914.675771777, -1796349.9876006693], [-5373985.14891314, -1796276.70089548], [-5374038.003852639, -1796240.0577453095], [-5374084.986021092, -1796233.9505399338], [-5374143.7136203395, -1796197.3073413963], [-5374114.3497650605, -1796117.9140584948], [-5374026.258310536, -1796075.163879226], [-5373991.021684198, -1796020.199478545], [-5374020.385539477, -1795946.913928899], [-5374073.240478986, -1795946.913928899], [-5374120.22253612, -1795934.69967379], [-5374096.731563207, -1795867.5213854203], [-5374043.876623697, -1795849.2001835033], [-5374008.639997371, -1795824.7717823782], [-5374032.131081592, -1795751.4867330224], [-5374038.003852639, -1795702.6302775636], [-5373991.021684198, -1795672.0949726838], [-5373938.166856006, -1795678.2020304438], [-5373879.43914545, -1795702.6302775636], [-5373785.474919865, -1795714.8444107503], [-5373726.7472092975, -1795714.8444107503], [-5373662.146839002, -1795659.8808619566], [-5373621.0374416085, -1795653.7738089992], [-5373579.928155532, -1795604.9174431076], [-5373579.928155532, -1795519.419165551], [-5373579.928155533, -1795476.6700868027], [-5373556.437071301, -1795421.7072167783], [-5373515.3276739195, -1795397.2792647923], [-5373474.218276524, -1795360.637384942], [-5373450.727303611, -1795293.4608709263], [-5373456.600074668, -1795238.498318172], [-5373462.472845728, -1795171.322156965], [-5373462.472845728, -1795079.7184556436], [-5373433.108990438, -1795030.8634115518], [-5373450.727303611, -1794939.2603791882], [-5373415.490677277, -1794878.1918142058], [-5373397.872364113, -1794792.696208329], [-5373368.508508824, -1794731.6280281371], [-5373350.890195659, -1794694.9871969742], [-5373309.780909575, -1794658.3465392806], [-5373368.508508824, -1794591.1719378487], [-5373438.981761496, -1794523.9976461316], [-5373480.091047582, -1794487.357141995], [-5373562.30984236, -1794414.0764225828], [-5373615.164670549, -1794371.3293753406], [-5373691.510694282, -1794334.6891116383], [-5373767.856606701, -1794304.1556026211], [-5373867.693603334, -1794224.7687824722], [-5373891.184687565, -1794182.0220830205], [-5373891.184687565, -1794102.635679566], [-5373897.057458614, -1794023.2495469442], [-5373961.657940239, -1793962.1833863326], [-5374038.003852639, -1793913.3305732128], [-5374043.876623697, -1793809.5188015886], [-5373991.021684198, -1793742.3467663827], [-5373932.294084949, -1793693.4944147258], [-5373855.948061219, -1793632.4292351068], [-5373803.093233029, -1793565.2575952308], [-5373814.838775145, -1793479.7668133222], [-5373902.930229671, -1793430.9150127745], [-5373979.27614208, -1793406.4891509512], [-5374026.258310536, -1793320.998952138], [-5374032.131081592, -1793241.6154854838], [-5374038.003852639, -1793150.0193806088], [-5374020.385539477, -1793082.849248477], [-5373949.912398123, -1793088.9556266405], [-5373873.566374391, -1793113.381039564], [-5373838.329859375, -1793113.381039564], [-5373791.347690923, -1793052.3173816907], [-5373767.856606701, -1792979.0411805015], [-5373767.856606701, -1792911.8714259006], [-5373838.329859375, -1792814.1704262954], [-5373897.057458614, -1792808.0641201958], [-5374008.639997371, -1792771.4263172369], [-5374008.639997371, -1792710.363556144], [-5373932.29408495, -1792643.1945765675], [-5373844.202630422, -1792618.7695411236], [-5373779.602148809, -1792582.1321517185], [-5373773.729377761, -1792502.7510883247], [-5373814.838775145, -1792441.6890317118], [-5373991.021684198, -1792374.5208270375], [-5374055.622165811, -1792356.2022593745], [-5374067.367707927, -1792270.7159165493], [-5374055.622165811, -1792191.3360302683], [-5374014.512768418, -1792118.0624223526], [-5373973.403482343, -1792063.1074834121], [-5373926.42131389, -1792026.4708523185], [-5373838.329859375, -1791989.8342788438], [-5373814.838775145, -1791910.4553497154], [-5373808.966004087, -1791806.6523914419], [-5373779.602148809, -1791678.425961957], [-5373744.365522469, -1791623.472060066], [-5373709.129007453, -1791519.6704961057], [-5373691.510694281, -1791464.7168528805], [-5373626.910212667, -1791403.6575170436], [-5373579.928155532, -1791342.598225447], [-5373574.055384475, -1791232.6920194821], [-5373591.673697648, -1791165.527300272], [-5373662.146839002, -1791116.680469064], [-5373773.729377759, -1791061.7277761195], [-5373867.693603334, -1791012.8811624572], [-5373914.675771777, -1790976.2461825889], [-5373961.657940238, -1790902.9763955974], [-5373996.894455256, -1790823.601168654], [-5373996.894455256, -1790750.3319772135], [-5373991.021684198, -1790664.8514099123], [-5373991.021684198, -1790567.15983252], [-5373944.039627066, -1790463.362972849], [-5374002.767226313, -1790365.6721240238], [-5374102.604222947, -1790322.932622105], [-5374208.314101955, -1790316.8269688352], [-5374290.532785424, -1790200.8199762285], [-5374319.896640711, -1790078.7079698436], [-5374314.023869655, -1789956.596602897], [-5374302.27832754, -1789877.224482026], [-5374249.423388029, -1789828.380349963], [-5374167.204704561, -1789803.958264418], [-5374108.476994003, -1789749.0086654508], [-5374073.240478986, -1789700.1648019054], [-5374073.240478986, -1789614.6880842736], [-5374126.095307176, -1789547.5281416213], [-5374190.6957887905, -1789492.5791467682], [-5374243.550616982, -1789449.841129163], [-5374314.023869655, -1789425.4195556887], [-5374372.751468892, -1789443.735704465], [-5374437.351950518, -1789486.4737108857], [-5374496.079549757, -1789486.4737108839], [-5374496.079549757, -1789443.735704465], [-5374466.715805798, -1789382.681661115], [-5374425.606408403, -1789333.8384489072], [-5374390.369782066, -1789297.2062225929], [-5374472.588576855, -1789291.1008378426], [-5374548.934489266, -1789278.8900731476], [-5374531.316176092, -1789205.6256191446], [-5374484.334007641, -1789144.5721989502], [-5374437.351950518, -1789101.8348188696], [-5374396.242553124, -1789040.7816702777], [-5374390.369782066, -1788943.0967796773], [-5374413.860866288, -1788863.7282229022], [-5374472.588576855, -1788808.7808385582], [-5374513.69786293, -1788747.7284567512], [-5374525.443405035, -1788686.6761189573], [-5374537.1889471505, -1788631.7292672233], [-5374584.171115602, -1788582.8876269911], [-5374625.280401677, -1788527.940903897], [-5374625.280401677, -1788454.678923399], [-5374613.534859561, -1788405.837653676], [-5374613.534859561, -1788338.6811904102], [-5374572.425573487, -1788302.0504105885], [-5374525.443405035, -1788234.894245937], [-5374543.061718209, -1788155.5279818715], [-5374584.171115602, -1788125.002683271], [-5374672.26257013, -1788167.7381587815], [-5374707.499196465, -1788155.5279818715], [-5374707.499196465, -1788021.216573142], [-5374748.60848254, -1787941.8511512731], [-5374807.336193107, -1787893.0109545526], [-5374871.936563403, -1787868.591010224], [-5374936.537045019, -1787831.9609680513], [-5374965.900900309, -1787801.4359768115], [-5374942.409816077, -1787734.2812522778], [-5374871.936563403, -1787709.861358249], [-5374824.9545062715, -1787722.0713020698], [-5374766.226795704, -1787685.4414897712], [-5374777.97233782, -1787630.4968790635], [-5374777.97233782, -1787569.4475789776], [-5374801.463422052, -1787465.6640541232], [-5374836.700048387, -1787404.6150692499], [-5374918.918731856, -1787361.880874802], [-5374936.537045019, -1787306.937141144], [-5374942.409816077, -1787221.469187928], [-5374930.664273972, -1787129.8968437102], [-5374960.028129249, -1787032.2200645627], [-5374983.5191021515, -1786971.1722127115], [-5375030.501270604, -1786928.4389270614], [-5375030.501270604, -1786861.2865971585], [-5375030.501270604, -1786757.506219092], [-5375065.73789694, -1786696.4590851867], [-5375118.592725131, -1786635.412226516], [-5375089.228981171, -1786574.3654116492], [-5375053.9923548335, -1786525.528190325], [-5375007.010186374, -1786476.6909553832], [-5374948.282587135, -1786458.3770185912], [-5374895.427647624, -1786452.2723761844], [-5374895.427647624, -1786360.7030472201], [-5374930.664273973, -1786287.4478655593], [-5374995.264644266, -1786244.7157148204], [-5375030.501270604, -1786134.8335160448], [-5375001.137415324, -1786049.369850879], [-5374995.264644266, -1785988.3246828374], [-5374983.5191021515, -1785915.070552801], [-5374989.391873209, -1785799.085432215], [-5374971.773671365, -1785725.8320110368], [-5374971.773671365, -1785628.1611013452], [-5374918.918731856, -1785579.3257416913], [-5374889.554876567, -1785506.0730092274], [-5374848.445590503, -1785451.1335237334], [-5374777.97233782, -1785390.0898023965], [-5374742.735711481, -1785292.4202955295], [-5374689.880883303, -1785206.959826195], [-5374678.135341187, -1785090.9782190043], [-5374684.008112244, -1785023.8311687], [-5374666.345716548, -1784964.1659423823], [-5374660.517028013, -1784944.4759281196], [-5374631.153172736, -1784889.5377622927], [-5374642.8987148395, -1784791.8703463494], [-5374642.8987148395, -1784730.8283462143], [-5374642.8987148395, -1784645.369929392], [-5374642.8987148395, -1784572.1201229203], [-5374607.662088506, -1784486.662170333], [-5374572.425573487, -1784419.5169709192], [-5374572.425573487, -1784346.2677560821], [-5374554.807260322, -1784273.0188863361], [-5374496.079549757, -1784211.9782403386], [-5374402.115324172, -1784163.1459539293], [-5374337.514953876, -1784114.3136537543], [-5374284.660014368, -1784077.6894955228], [-5374249.423388029, -1784028.8574894448], [-5374225.932415129, -1783973.9214742675], [-5374167.204704561, -1783912.8817244025], [-5374131.968078224, -1783857.9459815554], [-5374108.476994003, -1783796.9065342655], [-5374090.858792149, -1783729.7631989077], [-5374079.113250044, -1783687.0357221777], [-5374049.749394755, -1783632.1006253094], [-5374032.131081592, -1783583.2694335124], [-5373985.14891314, -1783546.6462222433], [-5373938.166856006, -1783491.7113392502], [-5373838.329859375, -1783503.919079885], [-5373756.111064587, -1783424.568879615], [-5373626.910212666, -1783357.4267283287], [-5373585.80092659, -1783284.180954279], [-5373527.073216033, -1783223.142887927], [-5373497.709360746, -1783119.3786561685], [-5373444.854532554, -1783033.9260095519], [-5373374.38127988, -1782985.0961815368], [-5373321.526451691, -1782966.784978912], [-5373204.071141884, -1782991.199918931], [-5373157.088973444, -1782954.5775184475], [-5373151.216202386, -1782875.2292963192], [-5373139.470660271, -1782759.259248245], [-5373151.216202386, -1782624.9788393932], [-5373121.852347097, -1782533.4243778724], [-5373068.997518918, -1782454.0775831826], [-5373039.6336636385, -1782399.145264277], [-5372986.778724129, -1782368.6274807157], [-5372916.305582775, -1782399.145264277], [-5372881.068956428, -1782484.5955938122], [-5372839.9595590355, -1782557.838865933], [-5372781.2319597965, -1782606.667918443], [-5372734.249791345, -1782533.4243778724], [-5372734.249791345, -1782399.145264277], [-5372699.013276327, -1782332.0060541104], [-5372646.158336817, -1782289.2811287919], [-5372587.430737579, -1782240.4527381675], [-5372516.957484896, -1782222.1422336227], [-5372458.229885657, -1782222.1422336227], [-5372417.120488264, -1782216.0386967252], [-5372376.011090869, -1782179.4175087684], [-5372346.647235581, -1782148.8998958687], [-5372323.15626268, -1782130.5893472296], [-5372264.428552123, -1782081.7614031916], [-5372205.7009528745, -1782051.2439175637], [-5372164.59155548, -1781996.312543639], [-5372164.59155548, -1781916.967568907], [-5372164.59155548, -1781880.346848607], [-5372070.627329904, -1781868.1399545614], [-5371994.281306164, -1781862.0365099246], [-5371959.044679838, -1781837.6228629719], [-5371900.317080589, -1781801.0022667407], [-5371829.843939236, -1781752.1748941503], [-5371776.988999726, -1781739.9680669028], [-5371653.6609188635, -1781770.485146937], [-5371536.205609058, -1781782.6919900873], [-5371483.350669548, -1781782.6919900873], [-5371395.259215032, -1781819.3125576996], [-5371324.786073668, -1781813.2091257954], [-5371248.440049937, -1781801.0022667407], [-5371177.966908574, -1781770.485146937], [-5371130.984740132, -1781691.140937234], [-5371089.875342736, -1781630.107023673], [-5370990.038346095, -1781605.6935027696], [-5370978.29280399, -1781581.280123009], [-5371042.893285605, -1781520.2464956874], [-5371054.638827721, -1781428.696468565], [-5370990.038346095, -1781379.869872015], [-5370925.4379758, -1781373.7665546008], [-5370843.219181011, -1781385.9731910212], [-5370790.36435283, -1781392.0765116038], [-5370743.382184379, -1781379.8698720161], [-5370731.636642262, -1781331.0433772295], [-5370743.38218438, -1781239.494089465], [-5370725.763871205, -1781166.2549399629], [-5370684.654585129, -1781154.0484180192], [-5370567.199275325, -1781160.1516781962], [-5370426.252881298, -1781184.5647348042], [-5370332.288544394, -1781276.1138079164], [-5370326.415773336, -1781410.386482928], [-5370232.451547751, -1781459.213027392], [-5370197.214921415, -1781453.1096893065], [-5370161.978406399, -1781428.696468566], [-5370126.741780062, -1781392.0765116038], [-5370114.996237948, -1781343.2499913776], [-5370091.505153726, -1781288.3203934564], [-5370120.869009006, -1781166.2549399629], [-5370150.232864282, -1781068.6029424872], [-5370156.10563534, -1780952.641839767], [-5370161.978406399, -1780867.1972143573], [-5370132.6145511195, -1780732.9276520538], [-5370114.996237947, -1780696.3087824816], [-5370073.886840554, -1780745.1339546298], [-5370050.395867651, -1780824.4749606398], [-5370003.413699198, -1780824.4749606398], [-5369974.049843919, -1780812.2686167383], [-5369915.322244673, -1780775.6497388748], [-5369885.958389393, -1780720.7213558352], [-5369815.485136719, -1780647.4838277367], [-5369768.503079586, -1780488.8031669974], [-5369733.26645325, -1780397.2571695733], [-5369692.157055857, -1780262.9902994458], [-5369703.902597972, -1780171.445068246], [-5369745.0119953565, -1780098.209256351], [-5369786.121392751, -1780000.561824489], [-5369797.866934867, -1779927.3264307478], [-5369774.375850645, -1779841.8855428325], [-5369680.411625061, -1779805.2680651813], [-5369615.811143447, -1779805.2680651813], [-5369527.719688919, -1779811.370974154], [-5369422.009921218, -1779774.7535441583], [-5369339.79112644, -1779768.650644717], [-5369245.826900857, -1779744.2391785255], [-5369163.6082173865, -1779695.4160910188], [-5369175.353759493, -1779579.46178129], [-5369204.717503462, -1779481.81650976], [-5369239.954129797, -1779396.3771294712], [-5369239.954129797, -1779304.8353973923], [-5369216.463045577, -1779219.3967775407], [-5369187.099190288, -1779115.650159938], [-5369128.371591049, -1779005.8014159626], [-5369024.075580882, -1778927.1446652634], [-5368773.504312665, -1778796.9545654147], [-5368550.774160418, -1778695.6961626438], [-5368244.52032631, -1778449.7847677646], [-5367673.77449903, -1778146.0153651054], [-5367297.9174853815, -1777740.9954954865], [-5367184.377282064, -1777514.078977753], [-5367145.22554923, -1777261.8516979972], [-5367043.430999358, -1776936.4010688139], [-5366886.823845398, -1776529.5942116787], [-5366745.877451371, -1776334.3293350795], [-5366620.591817259, -1776106.5224987413], [-5366495.306183156, -1775789.2236560252], [-5366432.663254781, -1775431.250665689], [-5366424.832930475, -1775162.7745011689], [-5366518.797156059, -1774406.1763943403], [-5366503.136507454, -1774113.3062065386], [-5366417.0026061665, -1773836.709984853], [-5366362.190113417, -1773324.2020154314], [-5366355.925831717, -1772852.3791110257], [-5366340.265071793, -1772413.1042083658], [-5366434.229408698, -1772047.0481090778], [-5366653.479268384, -1771664.7288719956], [-5366872.729239391, -1771420.6985962237], [-5367156.188070052, -1771251.5057960479], [-5367422.420098191, -1771048.1505864912], [-5367641.670069196, -1770926.1383265082], [-5367790.446787532, -1770893.601838267], [-5367900.071773039, -1770893.601838267], [-5368017.527082847, -1770934.2724266723], [-5368119.321632728, -1770950.5407510754], [-5368252.437702457, -1771056.2847287827], [-5368338.571603733, -1771105.0898728315], [-5368409.044745097, -1771129.4924827681], [-5368518.669730596, -1771113.2240347948], [-5368589.142871951, -1771048.1505864912], [-5368675.2767732255, -1770999.3455604084], [-5368777.071434438, -1771007.4798014879], [-5368847.544575792, -1771056.2847287827], [-5368972.830321214, -1771105.0898728315], [-5369098.115955326, -1771105.0898728315], [-5369231.232025055, -1771048.1505864912], [-5369387.8390676975, -1770991.2114377767], [-5369513.1247018, -1770966.808971071], [-5369583.597954471, -1770966.808971071], [-5369654.071095826, -1770918.004229143], [-5369771.526405632, -1770885.467752145], [-5369865.490742535, -1770909.8700189572], [-5369975.115616724, -1770828.5289966215], [-5370069.079953617, -1770722.7859255378], [-5370147.383419279, -1770551.9713668153], [-5370147.383419279, -1770446.2295368474], [-5370116.0620107595, -1770242.8812775125], [-5370014.267460869, -1770072.0701973527], [-5370006.437136572, -1769860.591433841], [-5369967.285292417, -1769730.451519277], [-5370014.267460869, -1769518.975820469], [-5370131.722770676, -1769388.8379073075], [-5370202.195912026, -1769193.63226871], [-5370155.213854906, -1768990.2948138956], [-5370163.044179201, -1768778.8257530467], [-5370210.026347655, -1768559.225304689], [-5370170.87450351, -1768274.5611080655], [-5370084.740602223, -1768054.965238145], [-5370053.419193704, -1767932.968467134], [-5370006.437136573, -1767688.9768172642], [-5369896.812151064, -1767469.3865122877], [-5369826.33889839, -1767322.994072112], [-5369748.035432731, -1767087.141501231], [-5369622.749687307, -1766745.5661609592], [-5369505.294377502, -1766509.7193583392], [-5369442.651560444, -1766306.405027751], [-5369434.821236139, -1766094.9600129796], [-5369426.990800522, -1765875.3845322125], [-5369466.142644666, -1765672.0756617056], [-5369489.633728898, -1765476.9007596422], [-5369489.633728898, -1765338.6529569316], [-5369419.160476214, -1765208.5379739683], [-5369411.3301519165, -1765045.8953972186], [-5369450.481884744, -1764915.7821417197], [-5369497.464053194, -1764834.4616629668], [-5369575.767630176, -1764688.0855525108], [-5369614.919362999, -1764501.0507753792], [-5369614.919362999, -1764281.4899884854], [-5369599.258603077, -1764135.117417448], [-5369585.985868868, -1764109.8473733577], [-5369552.276545945, -1764045.667894782], [-5369520.955137415, -1763883.0332028386], [-5369505.294377503, -1763736.6629826417], [-5369434.821236139, -1763492.7146671969], [-5369372.178419093, -1763305.6893446578], [-5369254.723109286, -1763134.9283247115], [-5369160.7587723825, -1763012.956955262], [-5369004.15172974, -1762890.9862146159], [-5368886.696419936, -1762736.490853441], [-5368839.714251494, -1762533.2089715993], [-5368753.580350207, -1762346.191233362], [-5368643.955364707, -1762216.092828883], [-5368557.821463421, -1762077.8639709484], [-5368440.3661536155, -1761947.7670412709], [-5368275.928786677, -1761768.884872537], [-5368111.49130843, -1761687.575211124], [-5367947.053830163, -1761581.8730572003], [-5367837.428844663, -1761435.5171855162], [-5367727.803970474, -1761313.5544574684], [-5367657.330717804, -1761183.4617273444], [-5367602.518225052, -1761118.415630269], [-5367445.911182422, -1761045.2390135014], [-5367289.304028463, -1761045.2390135014], [-5367164.018394349, -1761045.2390135014], [-5367030.902324619, -1761045.2390135014], [-5366874.2952819895, -1761053.3697118515], [-5366717.688239359, -1761061.5004129822], [-5366584.572169628, -1761077.761939219], [-5366252.60253126, -1760615.5089750031], [-5365852.541766023, -1760501.0822372749], [-5365764.486379012, -1758435.640988037], [-5365723.975212564, -1757484.116131998], [-5365683.464046103, -1756532.6294287073], [-5365577.4925662875, -1756012.015678607], [-5365628.986068421, -1754111.5869924452], [-5365596.948764247, -1747786.4383183168], [-5365602.376368663, -1747324.529217402], [-5365607.8039730815, -1746862.6289424906], [-5364668.849671717, -1746886.9191658471], [-5363729.895370362, -1746911.209298395], [-5361813.689523221, -1746851.489159326], [-5359897.483676091, -1746791.7691696752], [-5352572.4396561105, -1746684.9223479037], [-5351879.33411621, -1746674.8123919119], [-5349230.683979601, -1746562.7521275333], [-5349127.935087724, -1746558.4050195087], [-5343384.081686566, -1746869.8343221222] ] ] ] }, \"pais\": { \"id\": 1, \"descricao\": \"Brasil\" }, \"ufImovel\": \"DF\", \"municipioImovel\": \"Brasília\" }, \"proprietario\": { \"tipoProprietarioReal\": { \"id\": 5, \"descricao\": \"Autarquia/Fundação\", \"ativo\": true, \"codigoSistemico\": \"INC_FORM_PROPRIETARIO_AUTARQ_PROP\" }, \"isIntegralmente\": \"true\", \"unidadeUsuario\": \"Serviço Federal de Processamento de Dados\", \"codigoUg\": 15132 }, \"proprietarioTitular\": { \"tipoProprietarioReal\": {} }, \"unidadeAutonoma\": { \"area\": null, \"memorialDescritivo\": null, \"numeroDormitorios\": null, \"possuiGaragem\": null, \"numeroVagasCobertas\": null, \"numeroVagasDescobertas\": null, \"observacoes\": null, \"tipoGaragem\": null, \"condicoesAcessibilidade\": { \"semInformacoesAcessibilidade\": null, \"vaga\": null, \"mobilidade\": null, \"banheiro\": null, \"elevador\": null, \"vagasDefFisicos\": null, \"vagasIdosos\": null, \"calcadasRebaixa\": null, \"calcadasLivres\": null, \"entradaLivre\": null, \"pavimentosLivreCadRodas\": null, \"balcaoInformacoes\": null, \"pisoTatil\": null, \"corredorLargo\": null, \"sanitarioPublico\": null, \"braile\": null, \"libras\": null, \"possuiLaudo\": null, \"linkLaudo\": null, \"possuiPlanoTrabalho\": null, \"linkPlanoTrabalho\": null }, \"edificacao\": { \"possuiElevador\": null, \"nome\": null, \"observacoes\": null, \"arquivoGeometria\": {}, \"tipoEdificacao\": {}, \"tipoProjeto\": null, \"documentacao\": [ { \"nome\": \"Plano de Proteção Contra Incêndio\", \"possui\": \"SEM_INFORMACAO\", \"isPrecadastrado\": true }, { \"nome\": \"Habite-se\", \"possui\": \"SEM_INFORMACAO\", \"isPrecadastrado\": true }, { \"nome\": \"Alvará de Construção/Reforma\", \"possui\": \"SEM_INFORMACAO\", \"isPrecadastrado\": true }, { \"nome\": \"Laudo de Inspeção Predial\", \"possui\": \"SEM_INFORMACAO\", \"isPrecadastrado\": true }, { \"nome\": \"Relatório de Acessibilidade\", \"possui\": \"SEM_INFORMACAO\", \"isPrecadastrado\": true } ], \"dadosMedidor\": [] }, \"fotos\": [], \"plantas\": [], \"documentos\": [] }, \"incorporacao\": { \"matriculas\": [ { \"uf\": \"DF\", \"cartorio\": { \"id\": 3276, \"nome\": \"3º OFICIAL DO REGISTRO DE IMÓVEIS DO DISTRITO FEDERAL\", \"uf\": \"DF\", \"municipio\": \"Brasília\", \"numeroMunicipio\": \"5300108\" }, \"municipio\": \"Brasília\", \"numero\": \"54567\", \"dataRegistro\": \"2023-09-05T03:00:00.000Z\", \"cartorioID\": 3276 } ], \"certidoesCartoriais\": [ { \"arquivo\": { \"arquivo\": \"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\", \"nomeOriginal\": \"pdf2pags.pdf\", \"tipoArquivo\": \"application/pdf\" }, \"descricao\": \"tgfvcdxs\", \"cartorio\": 3276, \"data\": \"2023-09-06T03:00:00.000Z\", \"url\": \"blob:http://localhost:3000/410f36ff-b1fc-4121-a7d0-4c7bf16afc52\" } ], \"tipoIncorporacao\": { \"id\": 2, \"descricao\": \"Por Aquisição\", \"codigoSistemico\": \"INC_FORM_DADOS_GERAIS_AQUISICAO\", \"ordem\": 1 }, \"situacaoIncorporacao\": { \"id\": 1, \"descricao\": \"Incorporado\", \"codigoSistemico\": \"INC_FORM_DADOS_GERAIS_INCORPORADO\" }, \"tipoAquisicao\": { \"id\": 2, \"codigoSistemico\": \"INC_FORM_DADOS_GERAIS_COMPRA\", \"descricao\": \"Compra\", \"ordem\": 1 }, \"direitoIncorporado\": { \"id\": 1, \"descricao\": \"Propriedade\", \"ordem\": 1, \"ativo\": true, \"codigoSistemico\": \"INC_FORM_DADOS_GERAIS_PROPRIEDADE\" } }, \"terreno\": { \"unidadeMedidaCadastral\": \"m2\", \"unidadeMedidaCartorial\": \"m2\", \"areaTotalCadastral\": 100, \"tipoGeometriaCadastral\": \"VETORIZACAO\", \"geometriaTerreno\": { \"type\": \"Polygon\", \"coordinates\": [ [ [-5330071.415079795, -1783447.1292490154], [-5329863.601909144, -1783425.6313348103], [-5329854.047280609, -1783561.7847914433], [-5329999.755365777, -1783592.837334184], [-5330071.415079795, -1783447.1292490154] ] ] }, \"memorialDescritivoCadastral\": \"memorial do terreno 2\" }, \"titulos\": [ { \"tipoTituloAquisitivo\": { \"id\": 1, \"descricao\": \"Contrato/Termo\", \"ordem\": 1, \"codigoSistemico\": \"INC_FORM_TITULO_AQUIS_CONTRATO\" }, \"encargos\": [], \"nome\": \"teste\", \"arquivo\": \"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\", \"nomeArquivo\": \"pdf2pags.pdf\", \"dataAssinatura\": \"2023-09-04T03:00:00.000Z\" } ], \"benfeitorias\": [], \"matriculas\": [], \"ripsFusao\": [], \"dsSpiunet\": \"pend. sincronizacao\", \"proprietariosTerceiros\": [], \"proprietariosParciais\": [], \"proprietariosCartorio\": [], \"certidoesCartorial\": [], \"imagens\": [], \"arquivosComplementaresLista\": [], \"imovelOriundo\": true, \"linkProcessoSei\": null, \"fusoUTM\": null, \"sistemaReferencia\": { \"name\": \"SIRGAS_2000\", \"descricao\": \"SIRGAS 2000\", \"codigo\": \"4674\", \"codigoSistemico\": \"INC_FORM_LOC_SIRGAS_2000\" }, \"tipoImovel\": { \"id\": 1, \"descricao\": \"Gleba/Terreno/Lote sem edificação\", \"vidaUtil\": { \"id\": 1, \"valor\": 50 }, \"ordem\": 2, \"ativo\": true, \"tipoPai\": null, \"codigoSistemico\": \"INC_FORM_LOC_LOTE_SEM_ED\" }, \"numero\": \"666\", \"pontoImovel\": { \"type\": \"Point\", \"coordinates\": [-5329858.824594876, -1783493.7080631268] }, \"naturezaImovel\": { \"descricao\": \"Urbano\", \"id\": 1 }, \"tipoProcessoSei\": \"processoExistenteNaoSPU\", \"proprietarioAnterior\": { \"pessoas\": [] }, \"cnpjProprietario\": null, \"registradoCartorio\": false, \"imagemMapa\": { \"arquivo\": \"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\", \"nomeOriginal\": \"mapaimg.png\", \"tipoArquivo\": \"image/png\" } }");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/titulo-aquisitivo/titulo-aquisitivo.html","<div layout=column flex style=\"padding: 10px; margin: auto\"><ng-form name=forms.inclusaoTituloForm><!-- TÍTULO AQUISITIVO --><div layout=row><md-input-container class=\"md-block margem-baixa-padrao\" flex><label id=tipoTituloAquisitivoLabel>Tipo</label><!--<md-select\n          campo-ajuda=\"INC_FORM_TITULO_AQUIS_TIPO_TIT\"\n          ng-model=\"titulo.tipoTituloAquisitivo\"\n          ng-model-options=\"{trackBy: \'$value.id\'}\"\n          required\n          flex\n          name=\"tipoTituloAquisitivo\"\n        >\n          <md-option\n            campo-ajuda=\"{{item.codigoSistemico}}\"\n            ng-repeat=\"item in tiposTituloAquisitivo\"\n            ng-value=\"item\"\n          >\n            {{item.descricao}}\n          </md-option>\n        </md-select>--><md-autocomplete md-input-id=idTipoTituloAquisitivoInput campo-ajuda=INC_FORM_TITULO_AQUIS_TIPO_TIT required flex name=tipoTituloAquisitivoField md-clear-button=!$mdAutocompleteCtrl.isDisabled md-no-cache=true md-selected-item=titulo.tipoTituloAquisitivo md-search-text=searchTipoTituloAquisitivo md-items=\"item in tiposTituloAquisitivo | filtro: \'descricao\': searchTipoTituloAquisitivo | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match ng-model=titulo.tipoTituloAquisitivo md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=tipoTituloAquisitivoLabel ng-disabled=isDetalhar()><md-item-template><span md-highlight-text=searchTipoTituloAquisitivo md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{searchTipoTituloAquisitivo}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex ng-if=\"titulo.tipoTituloAquisitivo.id == ID_ATO_LEGAL\"><label id=tipoAtoLegalLabel>Tipo de Ato Legal</label><!--\n          <md-select\n          campo-ajuda=\"INC_FORM_TITULO_AQUIS_TIPO_ATO\"\n          ng-model=\"titulo.tipoAtoLegal\"\n          ng-model-options=\"{trackBy: \'$value.id\'}\"\n          name=\"tipoAtoLegal\"\n          ng-required=\"titulo.tipoTituloAquisitivo.id == ID_ATO_LEGAL\"\n        >\n          <md-option\n            campo-ajuda=\"{{item.codigoSistemico}}\"\n            ng-repeat=\"item in tiposAtoLegal\"\n            ng-value=\"item\"\n          >\n            {{item.descricao}}\n          </md-option>\n        </md-select>--><md-autocomplete campo-ajuda=INC_FORM_TITULO_AQUIS_TIPO_ATO name=tipoAtoLegal ng-required=\"titulo.tipoTituloAquisitivo.id == ID_ATO_LEGAL\" md-clear-button=!$mdAutocompleteCtrl.isDisabled md-no-cache=true md-selected-item=titulo.tipoAtoLegal md-search-text=searchTipoAtoLegal md-items=\"item in tiposAtoLegal | filter:{descricao:searchTipoAtoLegal} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=tipoAtoLegalLabel ng-disabled=isDetalhar()><md-item-template><span md-highlight-text=searchTipoAtoLegal md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{searchTipoAtoLegal}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block flex><label>Número</label><input campo-ajuda=INC_FORM_TITULO_AQUIS_NUMERO ng-model=titulo.numero name=numero md-maxlength=60 maxlength=60 ng-disabled=isDetalhar()></md-input-container></div><div layout=row><md-input-container class=md-block flex=50><label>Nome</label><input campo-ajuda=INC_FORM_TITULO_AQUIS_NOME_ARQ ng-model=titulo.nome name=nome md-maxlength=150 maxlength=150 required ng-disabled=isDetalhar()></md-input-container><!-- ARQUIVO --><md-input-container class=md-block flex=60><ci-input-file model=ctrlTitulo.arquivoFilearquivoFile objeto=ctrlTitulo.arquivoFilearquivoFile accept=.pdf,.doc,.docx,.ods,.txt obrigatorio=true cif-onchange=uploadArquivo() botao=true desabilitado=isDetalhar()></ci-input-file></md-input-container><!-- FIM ARQUIVO --></div><div layout=row><md-input-container class=md-block flex><label>Data Assinatura</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_TITULO_AQUIS_DATA_ASS flex ng-model=titulo.dataAssinatura md-max-date=hoje name=dataAssinatura required ng-change=validarDataAssinatura() ng-disabled=isDetalhar()></md-datepicker></md-input-container><md-input-container class=md-block flex><label>Data Final da Vigência</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_TITULO_AQUIS_DT_FIM_VIG flex ng-model=titulo.dataFinalVigencia name=dataFinalVigencia md-min-date=dataMinFinal ng-change=validarDataFinal() ng-disabled=isDetalhar()></md-datepicker></md-input-container><md-input-container class=md-block flex ng-if=\"titulo.dataFinalVigencia != undefined\"><label>Data de Prorrogação da Vigência</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_TITULO_AQUIS_DT_PROR_VIG flex ng-model=titulo.dataProrrogacaoVigencia md-min-date=dateMinProrrogacao name=dataProrrogacaoVigencia inc-ajusta-tamanho ng-disabled=isDetalhar()></md-datepicker></md-input-container></div><!-- DADOS DA PUBLICAÇÃO --><fieldset class=fieldset ng-if=\"titulo.tipoTituloAquisitivo.id === ID_ATO_LEGAL\"><legend><label style=\"color: rgb(76, 175, 80)\">Dados da Publicação</label></legend><div layout-gt-sm=row flex style=\"max-height: 75px\"><md-input-container class=\"md-block margem-baixa-padrao\" flex><label>Página</label><input campo-ajuda=INC_FORM_TITULO_AQUIS_PAGINA type=number ng-model=titulo.publicacao.pagina name=pagina maxlength=4 ng-disabled=isDetalhar()></md-input-container><md-input-container class=\"md-block margem-baixa-padrao\" flex><label>Seção</label><input campo-ajuda=INC_FORM_TITULO_AQUIS_SECAO type=number ng-model=titulo.publicacao.secao name=secao maxlength=4 ng-disabled=isDetalhar()></md-input-container><md-input-container class=md-block flex><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_TITULO_AQUIS_DATA ng-model=titulo.publicacao.dataPublicacao md-placeholder=\"Data da Publicação\" md-max-date=hoje name=dataPublicacao ng-disabled=isDetalhar()></md-datepicker></md-input-container></div><a ng-if=\"titulo.publicacao.pagina != undefined && titulo.publicacao.secao != undefined && titulo.publicacao.dataPublicacao != undefined\" target=_blank ng-href=\"http://pesquisa.in.gov.br/imprensa/jsp/visualiza/index.jsp?jornal={{titulo.publicacao.secao}}&pagina={{titulo.publicacao.pagina}}&data={{titulo.publicacao.dataPublicacao | date:\'dd/MM/yyyy\'}}\">Ver publicação no Diário Oficial da União - DOU</a></fieldset><div layout=row flex><md-input-container ng-class=\"\" class=\"md-block margem-baixa-padrao\" flex=50><label>Moeda</label><md-select campo-ajuda=INC_FORM_TITULO_AQUIS_MOEDA ng-model=titulo.moeda ng-model-options=\"{trackBy: \'$value.id\'}\" flex name=moeda ng-required=\"titulo.valorContrato != undefined\" ng-disabled=isDetalhar()><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in moedas\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50><label>Valor do Contrato</label><input campo-ajuda=INC_FORM_TITULO_AQUIS_VALOR_CONTR type=text ui-number-mask value=\"{{titulo.valorContrato |number:2}}\" ng-model=titulo.valorContrato name=valorContrato ng-disabled=isDetalhar()></md-input-container></div><div layout-gt-sm=row flex><md-input-container class=md-block flex=100><label>Observações/Anotações</label><textarea campo-ajuda=INC_FORM_TITULO_AQUIS_OBS maxlength=2000 ng-model=titulo.observacao name=observacao md-maxlength=2000 ng-disabled=isDetalhar()></textarea></md-input-container></div><!-- ENCARGOS --><div ng-if=\"titulo.tipoTituloAquisitivo.id !== 4 && titulo.tipoTituloAquisitivo.id !== undefined\"><fieldset-encargos imovel=imovel titulo=titulo detail=isDetalhar()></fieldset-encargos></div></ng-form></div><div layout=row style=\"margin-bottom: 20px\" layout-align=\"end center\"><md-button class=\"md-secondary md-raised\" ng-click=cancelarInclusaoTitulo();>Fechar</md-button><md-button class=\"md-secondary md-raised\" ng-click=limparInclusaoTitulo();limparArquivo(); ng-if=!isDetalhar()>Limpar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=salvarTituloAquisitivo(); ng-if=!isDetalhar()>Gravar</md-button></div>");
$templateCache.put("scripts-cadastro-imovel/pages/home/view/componentes/informe.dialog.html","<md-dialog aria-label=Informes flex=60 ng-cloak><md-toolbar><div class=md-toolbar-tools><h2>Informes</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content ng-show=controleExibicao><div class=informe-item ng-repeat=\"item in informes\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><p class=informe-data>{{item.data | date : \'dd/MM/yyyy\'}}</p><h2 class=informe-titulo>{{item.titulo}}</h2><p class=informe-descricao>{{descricaoExtensa(item.descricao)}}<md-button class=btn-continue-lendo ng-click=exibirDescricao($index)>continue lendo</md-button></p></div></div><div class=md-dialog-content ng-show=!controleExibicao><div class=informe-item><h2 class=informe-titulo-v2>{{informeSelecionado.titulo}}</h2><p class=informe-data-v2>Publicado em {{informeSelecionado.data | date : \'dd/MM/yyyy\'}}</p><p class=informe-descricao>{{informeSelecionado.descricao}}</p><md-button class=\"md-primary md-raised\" ng-click=voltar()>Voltar</md-button></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/home/view/componentes/mensagem.dialog.html","<md-dialog aria-label=Mensagens flex=60 ng-cloak><md-toolbar><div class=md-toolbar-tools><h2>Mensagens</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><p class=mensagem ng-repeat=\"item in mensagens\" ng-model-options=\"{trackBy: \'$value.codigo\'}\"><a href=# class=link-mensagem>{{item.data | date : \'dd/MM/yyyy\'}} - {{item.assunto}}</a></p></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos-detalhar-modal.html","<md-dialog aria-label=\"Fotos do Imóvel\" flex=50><md-toolbar><div class=md-toolbar-tools><h2>Fotos do imóvel</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><inc-carousel imagens=slides start-index=startIndex></inc-carousel></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos-detalhar.html","<div layout=column><inc-carousel imagens=diCtrl.slides></inc-carousel><table ng-show=\"diCtrl.imovel.imagens.length>0\" class=mui-table style=\"width: 100% !important\"><thead><div class=barra-separadora ng-show=\"diCtrl.imovel.imagens.length>0\"></div></thead><tbody><tr ng-repeat=\"img in diCtrl.imovel.imagens\" ng-model-options=\"{trackBy: \'$value.id\'}\" style=\"text-align: center\"><td>{{img.descricao}}</td><td>{{img.data | date : \'dd/MM/yyyy\'}}</td></tr></tbody></table></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos.html","<div layout=column><!--	<md-button class=\" md-primary md-raised\" ng-hide=\"selectedIndex != 0 || vm.isDetail\"\n		ng-click=\"imovelCtrl.showUploadImagemDialog($event)\"\n		aria-label=\"upload foto\"> Adicionar foto </md-button>--><inc-carousel src=true imagens=imovel.imagens link-imagem=\"imovelCtrl.showModalImagemDialog(ev, 0)\"></inc-carousel><!--		<table ng-show=\"imovel.imagens.length>0\" class=\"mui-table\" style=\"width: 100% !important;\">\n			<thead>\n				<div class=\"barra-separadora\" ng-show=\"imovel.imagens.length>0\"></div>\n			</thead>s\n			<tbody>\n				<tr ng-repeat=\"img in imovel.imagens\" ng-model-options=\"{trackBy: \'$value.id\'}\" style=\"text-align: center;\">\n					<td>{{img.descricao}}</td>\n					<td>{{img.data | date : \'dd/MM/yyyy\'}}</td>\n					<td>\n						<ng-md-icon ng-hide=\"selectedIndex == 1 || vm.isDetail\" icon=\"delete\" style=\"fill:#788489\" size=\"18\" alt=\"Excluir\" ng-click=\"imovelCtrl.removerImagem($index)\"></ng-md-icon>\n					</td>\n				</tr>\n			</tbody>\n		</table>--></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/album-fotos/dialog-upload-fotos.html","<md-dialog aria-label=\"Upload de Imagens\" flex=60 ng-cloak><form name=uploadImagem><md-toolbar><div class=md-toolbar-tools><h2>Upload de Imagem</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><md-input-container class=md-block flex-gt-sm><label>Descrição</label><input name=fotoDescricao ng-model=imagem.descricao type=text required> <span class=form-error ng-show=\"uploadImagem.fotoDescricao.$error.required && submitted\">Campo de preenchimento obrigatório</span></md-input-container><div layout-gt-sm=column><md-datepicker ng-mask=39/19/9999 restrict=reject ng-model=imagem.data md-placeholder=\"Data da foto\" md-max-date=uploadImagemDialogCtrl.hoje required name=fotoData></md-datepicker><span class=form-error ng-show=\"uploadImagem.fotoData.$error.required && submitted\">Campo de preenchimento obrigatório</span></div></div><div layout-gt-sm=row layout-margin><input type=file ngf-select ng-model=imagem.imagem accept=\".bmp, .png, .jpg, .jpeg\" ngf-max-size=5MB ngf-model-invalid=errorFile required name=fotoImagem> <span class=form-error ng-show=\"uploadImagem.fotoImagem.$error.required && submitted\">Campo de preenchimento obrigatório</span></div><div layout=column><div layout-gt-sm=row layout-margin layout-align=\"end right\"><md-button ng-click=incluir() layout-align=right class=\"md-primary md-raised\" ng-disabled=uploadImagem.$invalid>Inserir</md-button></div></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-cancelar-imovel.html","<style>.tdDireita {\n    text-align: right;\n  }\n\n  label {\n    text-decoration: none;\n    font-weight: bold;\n  }</style><md-dialog aria-label=\"Localização do imóvel\" flex=40><ng-form name=forms.cancelarImovelForm ng-submit=incluir()><md-toolbar><div class=md-toolbar-tools><h2>Cancelar imóvel</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><md-input-container class=md-block><label>Data:</label><input campo-ajuda=INC_FORM_CANCEL_IMOVEL_DATA value=\"{{hoje | date : \'dd/MM/yyyy\'}}\" disabled></md-input-container><md-input-container class=md-block><label>Responsável:</label><input campo-ajuda=INC_FORM_CANCEL_IMOVEL_RESPONS value={{nomeCadastrador}} disabled></md-input-container><md-input-container class=md-block ng-class=\"!cancelamentoImovel.tipoCancelamentoImovel.id && submittedCancelamento?\'md-input-invalid\':null\" flex-gt-sm><label id=motivoCancelamentoLabel>Motivo:</label><!-- <md-select\n            campo-ajuda=\"INC_FORM_CANCEL_IMOVEL_MOTIVO\"\n            ng-model=\"cancelamentoImovel.tipoCancelamentoImovel.id\"\n            name=\"motivo\" required>\n            <md-option\n            campo-ajuda=\"{{motivo.codigoSistemico}}\"\n            ng-repeat=\"motivo in motivos\" value=\"{{motivo.id}}\">\n				      {{motivo.descricao}}\n				    </md-option>\n          </md-select> --><md-autocomplete campo-ajuda=INC_FORM_CANCEL_IMOVEL_MOTIVO name=motivo required md-clear-button=!$mdAutocompleteCtrl.isDisabled md-selected-item=cancelamentoImovel.tipoCancelamentoImovel md-search-text=$scope.searchMotivoCancelamento md-items=\"item in motivos | filter:{descricao:$scope.searchMotivoCancelamento} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=motivoCancelamentoLabel><md-item-template><span md-highlight-text=$scope.searchMotivoCancelamento md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchMotivoCancelamento}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block flex-gt-sm ng-class=\"forms.cancelarImovelForm.numeroProcesso.$error.required && submittedCancelamento?\'md-input-invalid md-input-focused\':null\"><label>N° de Processo:</label><input campo-ajuda=INC_FORM_CANCEL_IMOVEL_N_PROCESSO ng-required=true name=numeroProcesso ng-model=cancelamentoImovel.numeroProcesso></md-input-container><md-input-container class=md-block ng-class=\"cancelamentoImovel.tipoCancelamentoImovel.id == 3 ? \'md-input-invalid md-input-focused\':null\"><label>Justificativa:</label><textarea campo-ajuda=INC_FORM_CANCEL_IMOVEL_JUSTIFICAT rows=5 ng-model=cancelamentoImovel.justificativa ng-required=\"cancelamentoImovel.tipoCancelamentoImovel.id == 3 || cancelamentoImovel.tipoCancelamentoImovel.id == 8 || cancelamentoImovel.tipoCancelamentoImovel.id == 9\" name=justificativa>\n          </textarea></md-input-container><div layout=row layout-align=\"end right\"><md-button class=\"md-raised md-primary\" ng-click=fechar()>Fechar</md-button><md-button class=\"md-raised md-primary\" ng-click=salvarCancelamento(forms.cancelarImovelForm.$invalid)>Confirmar</md-button></div></div></md-dialog-content></ng-form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-localizacao-imovel.html","<md-dialog aria-label=\"Localização do imóvel\" flex=60 ng-cloak id=localizacaoMapa><form name=localizacaoImovel ng-submit=incluir()><md-toolbar><div class=md-toolbar-tools><h2>Localização do imóvel</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=mapaImovelConsultar><ci-mapa-incorporacao showminimapa=false tabindex=\"\" imovel=imovel action=verlocalizacao habilitar-desenho=false></ci-mapa-incorporacao></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/componentes-imovel/dialog-upload-certidao-cartorial.html","<md-dialog aria-label=\"Upload de Certidão Cartorial\" flex=60 ng-cloak><form name=uploadCertidaoCartorial enctype=multipart/form-data><md-toolbar><div class=md-toolbar-tools><h2>Upload de Certidão Cartorial</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><md-input-container class=md-block flex-gt-sm><label>Nome</label><input ng-model=certidaoCartorial.nome type=text required name=nome> <span class=form-error ng-show=\"uploadCertidaoCartorial.nome.$error.required && certidaoCartorialSubmitted\">Campo de preenchimento obrigatório</span></md-input-container><div layout=column><md-datepicker ng-mask=39/19/9999 restrict=reject class=special ng-model=certidaoCartorial.data name=dataDocumento md-placeholder=\"Data do documento\" md-max-date=hoje required name=data></md-datepicker><span class=form-error ng-show=\"uploadCertidaoCartorial.dataDocumento.$error.required && certidaoCartorialSubmitted\">Campo de preenchimento obrigatório</span></div></div><div layout-gt-sm=row layout-margin><input type=file ngf-select ng-model=certidaoCartorial.certidao accept=\".pdf, .png, .jpg\" ngf-max-size=10MB ngf-model-invalid=errorFile required name=arquivo id=input-file-id> <span class=form-error ng-show=\"uploadCertidaoCartorial.arquivo.$error.required && certidaoCartorialSubmitted\">Campo de preenchimento obrigatório</span><md-button ng-click=incluir() layout-align=right class=\"md-primary md-raised\">Inserir</md-button></div><div layout=row ng-show=\"certidoesCartorial.length > 0\"><md-content style=\"width: 100% !important\"><h4>Documentos cartoriais</h4><table class=mui-table style=\"width:100% !important\"><thead><tr><th style=\"width: 200px !important\">Nome da certidão</th><th>Data da emissão</th><th>Nome do arquivo</th><th>Ação</th></tr></thead><tbody><tr ng-repeat=\"item in certidoesCartorial\" ng-model-options=\"{trackBy: \'$value.id\'}\"><td>{{item.nome}}</td><td>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td>{{item.nomeArquivo}}</td><td><ng-md-icon icon=search style=fill:#788489 size=18 alt=\"Visualizar documento\" ng-click=visualizarCertidao(item.id)></ng-md-icon><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=removerCertidao($index)></ng-md-icon></td></tr></tbody></table></md-content></div><div layout-gt-sm=row layout-margin layout-align=\"end right\"><md-button layout-align=right class=\"md-primary md-raised\" ng-click=confirmar()>Confirmar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/editar-imovel.tmp.html","<h1>RIP: {{ vm.imovel.rip || \"-\" }} - Edição do Imóvel <span ng-if=vm.imovel.isSuspenso>- SUSPENSO {{vm.getRestricoes()}}</span></h1><h2 ng-if=\"vm.imovel.dsSpiunet && vm.imovel.dsSpiunet !== \'\' && vm.imovel.dsSpiunet !== \'pend. sincronizacao\'\" style=\"color: green; font-size: small\">RIP SPIU: {{vm.imovel.dsSpiunet || \"-\"}}</h2><inc-mensagem tipo=info ng-if=\"vm.carregou && !vm.podeEditar\" titulo=\"Edição restrita\">RIP com restrição de edição pois possui Utilização ativa.</inc-mensagem><div class=\"inc-mensagem-container inc-mensagem-erro\" layout=row ng-show=\"vm.temPendenciaMigracao() || vm.temPendenciaSincronizacao()\"><md-icon style=\"margin-top: 15px\">report</md-icon><div><!--Pendencia de sincronização--><p class=inc-mensagem-titulo ng-if=vm.temPendenciaSincronizacao()>Imóvel pendente de sincronização com o SPIUnet</p><p ng-show=\"vm.mostrarPendencia && vm.temPendenciaSincronizacao()\">Erro ao sincronizar: {{vm.erroSincronizacao}}</p><!--Pendencia de migração--><p class=inc-mensagem-titulo ng-if=vm.temPendenciaMigracao()>Imóvel oriundo da migração de imóveis do sistema SPIUnet, pendente de validação.</p><div ng-show=\"vm.mostrarPendencia && vm.temPendenciaMigracao()\"><p>Dicas de avaliação:</p><ul><li>Verifique o número do endereço do imóvel, ele não pode conter letras ou símbolos</li><li>Verifique o tipo de imóvel</li><li>Verifique o CEP do imóvel</li><li>Verifique o georeferenciamento do imóvel</li><li>Verifique o proprietário do imóvel</li></ul><p>Essa lista não é exaustiva, é necessária uma avaliação completa do cadastro.</p></div><button class=md-button ng-click=vm.alterarMostrarPendencia()>Mostrar {{vm.mostrarPendencia ? \'menos\' : \'mais\'}}</button></div></div><md-divider></md-divider><form name=formGeral><div layout=column ng-cloak flex=100 flex-xs=100><md-content><md-tabs md-border-bottom md-dynamic-height ng-show=\"!vm.isInclusaoBenfeitoria && !vm.mostraTabTituloAquisitivo && !vm.mostraTabFormDocumento\"><md-tab ng-repeat=\"tab in vm.tabs.tabs\" label={{tab.nome}} ng-disabled=\"tab.index != vm.tabs.selectedIndex\" md-selected=vm.tabs.selectedIndex><div ng-include=tab.edit></div></md-tab></md-tabs><md-tabs md-border-bottom md-dynamic-height ng-if=vm.isInclusaoBenfeitoria><md-tab label={{vm.tabs.tabInclusaoBenfeitoria.nome}}><inclusao-benfeitoria benfeitoria=vm.benfeitoria imovel=vm.imovel show-inclusao-benfeitoria=vm.isInclusaoBenfeitoria show-tab-form-documento=vm.mostraTabFormDocumento documento=vm.documentoBenfeitoria></inclusao-benfeitoria></md-tab></md-tabs><md-tabs md-border-bottom md-dynamic-height ng-if=vm.mostraTabTituloAquisitivo><md-tab label={{vm.tabs.tabInclusaoTituloAquisitivo.nome}}><titulo-aquisitivo action=detail titulo=vm.titulo titulo-backup=vm.tituloBackup imovel=vm.imovel show-titulo-aquisitivo=vm.mostraTabTituloAquisitivo query-pagination=vm.paginationTituloAquisitivo detail=!vm.podeEditar></titulo-aquisitivo><div ng-include=vm.tabs.tabInclusaoTituloAquisitivo.template></div></md-tab></md-tabs><md-tabs md-border-bottom md-dynamic-height ng-if=\"vm.mostraTabFormDocumento && (vm.imovel.tipoImovel.tipoPai === \'UNIDADE AUTÔNOMA\')\"><md-tab label={{vm.tabs.tabFormDocumento.nome}}><form-documento documento=vm.documento documento-backup=vm.documentoBackup show-form-documento=vm.mostraTabFormDocumento documentos=vm.imovel.unidadeAutonoma.edificacao.documentacao></form-documento></md-tab></md-tabs><md-tabs md-border-bottom md-dynamic-height ng-if=\"vm.mostraTabFormDocumento && (vm.imovel.tipoImovel.id == 2 || vm.imovel.tipoImovel.id == 3)\"><md-tab label={{vm.tabs.tabFormDocumento.nome}}><form-documento documento=vm.documentoBenfeitoria show-form-documento=vm.mostraTabFormDocumento is-benfeitoria=true show-inclusao-benfeitoria=vm.isInclusaoBenfeitoria documentos=vm.benfeitoria.edificacao.documentacao></form-documento></md-tab></md-tabs></md-content></div></form>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/fieldsets/matricula-transcricao-fieldset.html","<fieldset class=fieldset><style>table.md-table:not(.md-row-select) td.md-cell:nth-child(n+2):nth-last-child(n+2) {\n	padding: 2px 8px;\n}\n\ntable:not(.md-row-select) th.md-column:nth-child(n+2):nth-last-child(n+2){\n	padding: 2px 8px;\n}\n\ntable.md-table:not(.md-row-select) td.md-cell:nth-child(n+2):nth-last-child(n+2), table.md-table:not(.md-row-select) th.md-column:nth-child(n+2):nth-last-child(n+2) {\n	padding: 2px 8px;\n}</style><legend><label style=\"color: rgb(76, 175, 80)\">Matrícula/Transcrição</label></legend><div layout-gt-xs=row ng-if=\"!vm.isDetail && !vm.isEdit\"><!-- UF --><md-input-container class=md-block flex-gt-sm flex ng-show=\"imovel.enderecamento.pais.id === 1\"><label>UF</label><md-select class=secaoDadosLocalizacaoImovel ng-model=matricula.uf ng-model-options=\"{trackBy: \'$value\'}\" name=ufSelect placeholder=UF* ng-change=pesquisaMunicipio(matricula.uf)><md-option ng-repeat=\"uf in imovelCtrl.ufs\" ng-value=uf.sigla>{{uf.sigla}}</md-option></md-select></md-input-container><!-- Município --><md-input-container class=md-block flex-gt-sm flex ng-show=\"imovel.enderecamento.pais.id === 1\"><label>Município*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=matricula.municipio ng-model-options=\"{trackBy: \'$value.codigo\'}\" name=municipio placeholder=Município* ng-change=\"imovelCtrl.carregarCartorios(matricula.uf, matricula.municipio.codigo)\"><md-option ng-repeat=\"municipio in municipios | filter:nome | orderBy:\'nome\'\" ng-value=municipio>{{municipio.nome}}</md-option></md-select></md-input-container><!-- Nome do Cartório --><md-input-container class=md-block flex-gt-sm flex ng-show=\"imovel.enderecamento.pais.id === 1\"><label>Nome do Cartório*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=matricula.cartorio ng-model-options=\"{trackBy: \'$value.id\'}\" name=cartorio placeholder=\"Nome do Cartório*\"><md-option ng-repeat=\"cartorio in imovelCtrl.cartorios | filter:nome | orderBy:\'nome\'\" ng-value=cartorio>{{cartorio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-sm flex ng-show=\"!imovel.enderecamento.pais.id === 1\"><label>Identificação do Cartório</label><textarea class=secaoDadosLocalizacaoImovel ng-model=matricula.cartorioEstrangeiro md-maxlength=400></textarea></md-input-container></div><!-- Linha das Datas --><div layout-gt-xs=row class=layout-gt-xs-row ng-if=\"!vm.isDetail && !vm.isEdit\"><md-input-container class=flex-gt-xs flex-gt-xs ng-class=\"forms.imovelForm.numeroMatricula.$error.required && submittedImovel? \'md-input-invalid\': null\"><label>Matrícula/Transcrição*</label><input class=secaoDadosIncorporacao ng-model=matricula.numero name=numeroMatricula></md-input-container><div flex-gt-xs class=flex-gt-xs><label style=\"margin-left: 16px\" class=matriculas-cs-data-label>Data da Matrícula *<span ng-show=\"imovelCtrl.dataMatriculaIsRequired && imovelCtrl.validaCamposConformeTipoImovel()  && imovelCtrl.imovel.incorporacao.tipoIncorporacao.id !== 5\">*</span></label><md-datepicker ng-mask=39/19/9999 restrict=reject class=\"secaoDadosIncorporacao matricula-cs\" ng-model=matricula.dataMatricula md-placeholder=\"Data da matrícula\" md-max-date=imovelCtrl.hoje name=dataMatricula></md-datepicker></div><div flex-gt-xs class=flex-gt-xs><label style=\"margin-left: 20px\" class=matriculas-cs-data-label>Data de Registro<span ng-show=\"imovelCtrl.dataRegistroIsRequired && imovelCtrl.validaCamposConformeTipoImovel() && imovelCtrl.imovel.incorporacao.tipoIncorporacao.id !== 5\" *></span></label><md-datepicker ng-mask=39/19/9999 restrict=reject class=\"secaoDadosIncorporacao matricula-cs\" ng-model=matricula.dataRegistro md-placeholder=\"Data de registro\" md-max-date=imovelCtrl.hoje name=dataRegistro></md-datepicker></div></div><div class=mgt-50 layout=row layout-align=\"end center\" ng-if=\"!vm.isDetail && !vm.isEdit\"><md-button class=\"md-primary md-raised\" ng-click=adicionaMatricula(matricula) ng-show=\"matricula.index !== undefined\">Alterar</md-button><md-button class=\"md-primary md-raised\" ng-click=adicionaMatricula(matricula) ng-show=\"matricula.index === undefined\">Adicionar</md-button></div><!-- Tabela Template --><div class=mgt-25 layout-gt-xs=row layout-align=center ng-show=imovelCtrl.hasMatricula() ng-if=!vm.isDetail><md-content><md-table-container ng-class=matricula-cs><table md-table md-progress=promise><thead md-head><tr md-row><th md-column class=inc-text-center style=\"width: 150px\">Localização</th><th md-column class=inc-text-center style=\"width: 200px; text-align: center !important\">Cartório</th><th md-column class=inc-text-center style=\"width: 70px\">Matrícula/Transcrição</th><th md-column class=inc-text-center style=\"width: 50px\">Data da Matrícula</th><th md-column class=inc-text-center style=\"width: 50px\">Data do Registro</th><th md-column class=inc-text-center style=\"width: 100px\"></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"matricula in imovel.incorporacao.matriculas | limitTo: tabela.limit: (tabela.page - 1) * tabela.limit\"><td md-cell class=inc-text-center ng-show=\"imovel.enderecamento.pais.id === 1\">{{matricula.uf}}/{{matricula.municipio}}</td><td md-cell class=inc-text-center ng-show=\"!imovel.enderecamento.pais.id === 1\">{{matricula.cidadeExterior}}</td><td md-cell class=inc-text-center ng-show=\"imovel.enderecamento.pais.id === 1\">{{matricula.cartorio.nome}}</td><td md-cell class=inc-text-center ng-show=\"!imovel.enderecamento.pais.id === 1\">{{matricula.cartorioEstrangeiro}}</td><td md-cell class=inc-text-center>{{matricula.numero}}</td><td md-cell class=inc-text-center>{{matricula.dataMatricula | date:\'dd/MM/yyyy\'}}</td><td md-cell class=inc-text-center>{{matricula.dataRegistro | date:\'dd/MM/yyyy\'}}</td><td md-cell class=inc-text-center><div layout=row layout-align=\"center stretch\"><md-button class=\"md-icon-button matricula-cs\" alt=\"Editar Matrícula\"><ng-md-icon icon=create style=fill:#788489 ng-click=alteraMatricula(matricula)><md-tooltip>Editar Matrícula</md-tooltip></ng-md-icon></md-button><md-button class=\"md-icon-button matricula-cs\" alt=\"Remover Matrícula\"><ng-md-icon icon=delete style=fill:#788489 ng-click=confirmaRemocao(matricula)><md-tooltip>Remover Matrícula</md-tooltip></ng-md-icon></md-button></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabela.limit md-limit-options=tabela.limitsPage md-page=tabela.page md-total={{imovel.incorporacao.matriculas.length}} md-on-paginate=true></md-table-pagination></md-content></div><!-- Detalhar e Alterar --><div layout-gt-xs=row ng-show=vm.isEdit><!-- UF --><md-input-container class=md-block flex-gt-sm flex ng-show=\"vm.imovel.enderecamento.pais.id === 1\"><label>UF*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=vm.matricula.uf ng-model-options=\"{trackBy: \'$value\'}\" name=ufSelect placeholder=UF* ng-change=vm.pesquisaMunicipio(vm.matricula.uf)><md-option ng-repeat=\"uf in vm.ufs\" ng-value=uf.sigla>{{uf.sigla}}</md-option></md-select></md-input-container><!-- Município --><md-input-container class=md-block flex-gt-sm flex ng-show=\"vm.imovel.enderecamento.pais.id === 1\"><label>Município*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=vm.matricula.municipio ng-model-options=\"{trackBy: \'$value.codigo\'}\" name=municipio placeholder=Município* ng-change=\"vm.carregarCartorios(vm.matricula.uf, vm.matricula.municipio.codigo)\"><md-option ng-repeat=\"municipio in vm.municipios | filter:nome | orderBy:\'nome\'\" ng-value=municipio>{{municipio.nome}}</md-option></md-select></md-input-container><!-- Nome do Cartório --><md-input-container class=md-block flex-gt-sm flex ng-show=\"vm.imovel.enderecamento.pais.id === 1\"><label>Nome do Cartório*</label><md-select class=secaoDadosLocalizacaoImovel ng-model=vm.matricula.cartorio ng-model-options=\"{trackBy: \'$value.id\'}\" name=cartorio placeholder=\"Nome do Cartório*\"><md-option ng-repeat=\"cartorio in vm.cartorios | filter:nome | orderBy:\'nome\'\" ng-value=cartorio>{{cartorio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-sm flex ng-show=\"!vm.imovel.enderecamento.pais.id === 1\"><label>Identificação do Cartório</label><textarea class=secaoDadosLocalizacaoImovel ng-model=vm.matricula.cartorioEstrangeiro md-maxlength=400></textarea></md-input-container></div><!-- Linha das Datas --><div layout-gt-xs=row class=layout-gt-xs-row ng-show=vm.isEdit><md-input-container class=flex-gt-xs flex-gt-xs ng-class=\"forms.imovelForm.numeroMatricula.$error.required && submittedImovel? \'md-input-invalid\': null\"><label>Matrícula/Transcrição*</label><input class=secaoDadosIncorporacao ng-model=vm.matricula.numero name=numeroMatricula></md-input-container><div flex-gt-xs class=flex-gt-xs><label style=\"margin-left: 16px\" class=matriculas-cs-data-label>Data da Matrícula *<span ng-show=\"vm.dataMatriculaIsRequired && vm.validaCamposConformeTipoImovel()  && vm.imovel.incorporacao.tipoIncorporacao.id !== 5\">*</span></label><md-datepicker ng-mask=39/19/9999 restrict=reject class=\"secaoDadosIncorporacao matricula-cs\" ng-model=vm.matricula.dataMatricula md-placeholder=\"Data da matrícula\" md-max-date=vm.hoje name=dataMatricula></md-datepicker></div><div flex-gt-xs class=flex-gt-xs><label style=\"margin-left: 20px\" class=matriculas-cs-data-label>Data de Registro<span ng-show=\"vm.dataRegistroIsRequired && vm.validaCamposConformeTipoImovel() && vm.imovel.incorporacao.tipoIncorporacao.id !== 5\">*</span></label><md-datepicker ng-mask=39/19/9999 restrict=reject class=\"secaoDadosIncorporacao matricula-cs\" ng-model=vm.matricula.dataRegistro md-placeholder=\"Data de registro\" md-max-date=vm.hoje name=dataRegistro></md-datepicker></div></div><div class=mgt-50 layout=row layout-align=\"end center\" ng-if=vm.isEdit><md-button class=\"md-primary md-raised\" ng-click=vm.adicionaMatricula(vm.matricula) ng-show=\"vm.matricula.index !== undefined\">Alterar</md-button><md-button class=\"md-primary md-raised\" ng-click=vm.adicionaMatricula(vm.matricula) ng-show=\"vm.matricula.index === undefined\">Adicionar</md-button></div><!-- Tabela Detail e Edit --><div class=mgt-25 layout-gt-xs=row layout-align=center ng-show=true ng-if=\"vm.isDetail || vm.isEdit\"><md-content><md-table-container ng-class=matricula-cs><table md-table md-progress=promise><thead md-head><tr md-row><th md-column class=inc-text-center style=\"width: 150px\">Localização</th><th md-column class=inc-text-center style=\"width: 200px; text-align: center !important\">Cartório</th><th md-column class=inc-text-center style=\"width: 70px\">Matrícula/Transcrição</th><th md-column class=inc-text-center style=\"width: 50px\">Data da Matrícula</th><th md-column class=inc-text-center style=\"width: 50px\">Data do Registro</th><th ng-if=vm.isEdit md-column class=inc-text-center style=\"width: 100px\"></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"matricula in vm.imovel.incorporacao.matriculas | limitTo: tabela.limit: (tabela.page - 1) * tabela.limit\"><td md-cell class=inc-text-center ng-show=\"vm.imovel.enderecamento.pais.id === 1\">{{matricula.uf}}/{{matricula.municipio}}</td><td md-cell class=inc-text-center ng-show=\"!vm.imovel.enderecamento.pais.id === 1\">{{matricula.cidadeExterior}}</td><td md-cell class=inc-text-center ng-show=\"vm.imovel.enderecamento.pais.id === 1\">{{matricula.cartorio.nome}}</td><td md-cell class=inc-text-center ng-show=\"!vm.imovel.enderecamento.pais.id === 1\">{{matricula.cartorioEstrangeiro}}</td><td md-cell class=inc-text-center>{{matricula.numero}}</td><td md-cell class=inc-text-center>{{matricula.dataMatricula | date:\'dd/MM/yyyy\'}}</td><td md-cell class=inc-text-center>{{matricula.dataRegistro | date:\'dd/MM/yyyy\'}}</td><td md-cell class=inc-text-center ng-if=vm.isEdit><div layout=row layout-align=\"center stretch\"><md-button class=\"md-icon-button matricula-cs\" alt=\"Editar Matrícula\"><ng-md-icon icon=create style=fill:#788489 ng-click=vm.alteraMatricula(matricula)><md-tooltip>Editar Matrícula</md-tooltip></ng-md-icon></md-button><md-button class=\"md-icon-button matricula-cs\" alt=\"Remover Matrícula\"><ng-md-icon icon=delete style=fill:#788489 ng-click=vm.confirmaRemocao(matricula)><md-tooltip>Remover Matrícula</md-tooltip></ng-md-icon></md-button></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabela.limit md-limit-options=tabela.limitsPage md-page=tabela.page md-total={{vm.imovel.incorporacao.matriculas.length}} md-on-paginate=true></md-table-pagination></md-content></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/fieldsets/unificacao-imoveis-fieldset.html","<fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">Unificação de Imóveis</label></legend><div layout=column><div layout-gt-xs=row><md-input-container class=md-block flex><label>RIP do Imóvel Remembrado</label><input type=text class=secaoDadosIncorporacao ng-model=imovel.incorporacao.ripImovelRemembrado ng-change=validarRipFusao() maxlength=8></md-input-container></div><div layout=column layout-align=\"center end\"><md-button class=\"md-primary md-raised\" ng-click=incluirRip() ng-disabled=!imovelRemembramento>Adicionar</md-button></div><!-- Tabela --><div class=mgt-25 layout-gt-xs=row ng-if=hasRipFusao()><md-content flex-xs=100 flex=100><md-table-container><table md-table><thead md-head md-order=ordenacaoTabelaUnificacao><tr md-row><th md-column md-order-by=rip>RIP</th><th md-column>Endereço do Imóvel</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"dadosRip in imovel.ripsFusao | orderBy: ordenacaoTabelaUnificacao | limitTo: paginacaoUnificacao.limit: (paginacaoUnificacao.page - 1) * paginacaoUnificacao.limit\"><td md-cell>{{ dadosRip.rip }}</td><td md-cell>{{dadosRip.enderecamento.tipoLogradouro}} {{dadosRip.enderecamento.logradouro}}, Número {{dadosRip.numero}}, {{dadosRip.enderecamento.bairro}}, {{dadosRip.enderecamento.municipio}}/{{dadosRip.enderecamento.uf}}-{{dadosRip.enderecamento.pais.descricao}}. CEP: {{dadosRip.enderecamento.cep}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=removerRip(dadosRip) class=\"md-fab md-raised md-mini\" aria-label=Remover><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Remover</md-tooltip></md-icon></md-button><md-button ng-click=irParaDetalhamento(dadosRip.id) class=\"md-fab md-raised md-mini\" aria-label=\"Detalhar RIP de Origem\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Detalhar RIP de Origem</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacaoUnificacao.limit md-limit-options=paginacaoUnificacao.limitOptions md-page=paginacaoUnificacao.page md-total=\"{{ imovel.ripsFusao.length }}\" md-page-select ng-disabled=\"imovel.ripsFusao.length < 6\"></md-table-pagination></md-content></div></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/modals/dialog-comboUf.html","<md-dialog aria-label=\"Enviar dados do Imóvel para o SEI\" flex=40><form ng-cloak><md-toolbar><div class=md-toolbar-tools><h2>Informe a Localização do Imóvel!</h2><span flex></span></div></md-toolbar><div layout=column><md-dialog-content flex=50><md-input-container flex=40 layout-margin layout-padding><label>Unidade Federativa</label><md-select campo-ajuda=INC_FORM_LOC_UNID_FED ng-model=imovel.enderecamento.uf ng-required=true ng-disabled=false ng-change=buscaMunicipiosUF(imovel.enderecamento.uf) ng-focus=buscaMunicipiosUF(imovel.enderecamento.uf) ng-class=\"isDadoAlterado(\'imovel.enderecamento.uf\')\"><md-option ng-repeat=\"uf in ufs\" value={{uf}}>{{uf}}</md-option><!-- <md-option value=\"AC\">AC</md-option>\n          <md-option value=\"AL\">AL</md-option>\n          <md-option value=\"AM\">AM</md-option>\n          <md-option value=\"BA\">BA</md-option> --></md-select></md-input-container><md-input-container flex=40><label id=municipioLabel>Município</label><md-autocomplete campo-ajuda=INC_FORM_LOC_MUNIC name=municipio md-selected-item-change=setMunicipio(municipio.nome) ng-selected=municipio.nome md-clear-button=true md-selected-item=municipio.nome md-search-text=vm.searchMunicipio md-items=\"municipio in municipios | filter:{nome:vm.searchMunicipio} | orderByBr:\'nome\'\" md-item-text=municipio.nome md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o Município\" input-aria-labelledby=municipioLabel><md-item-template><span md-highlight-text=vm.searchMunicipio md-highlight-flags=^i>{{municipio.nome}}</span></md-item-template><md-not-found>Nenhum resultado encontrado.</md-not-found></md-autocomplete></md-input-container></md-dialog-content></div><md-dialog-actions layout=row><md-button layout-align=right class=\"md-raised md-primary\" ng-click=confirmarUf()>Confirmar</md-button></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/modals/dialog-confirmar-ug-responsavel.html","<md-dialog aria-label=\"Confirmação da UG Responsável\" flex=50 ng-cloak><md-toolbar><div class=md-toolbar-tools><h2>Confirmar UG Responsável</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=column layout-padding><p style=\"font-size: 16px; margin-bottom: 20px\">Antes de prosseguir com o cadastro do imóvel, confirme se a <strong>UG Responsável</strong> está correta:</p><md-card style=\"background-color: #f5f5f5; padding: 16px; margin-bottom: 20px\"><md-card-content><div layout=column><div layout=row layout-align=\"start center\" style=\"margin-bottom: 12px\"><strong style=\"min-width: 150px\">Proprietário:</strong> <span>{{ proprietario.tipoProprietarioReal.descricao }}</span></div><div layout=row layout-align=\"start center\"><strong style=\"min-width: 150px\">UG Responsável:</strong> <span style=\"color: #2196F3; font-weight: 500; font-size: 15px\">{{ ugResponsavel || \'Não informada\' }}</span></div></div></md-card-content></md-card><div ng-if=!ugResponsavel style=\"background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 12px; margin-bottom: 16px\"><p style=\"margin: 0; color: #856404\"><md-icon md-svg-src=assets/img/icons/ic_warning_24px.svg style=\"color: #ffc107; vertical-align: middle; margin-right: 8px\"></md-icon><strong>Atenção:</strong> A UG Responsável não foi preenchida. Verifique os dados do proprietário.</p></div><p style=\"font-size: 14px; color: #666\">A UG Responsável é preenchida automaticamente pelo sistema com base nos dados do proprietário. Caso esteja incorreta, volte e revise as informações antes de salvar.</p></div></div></md-dialog-content><md-dialog-actions layout=row><md-button ng-click=fechar() class=md-raised>Voltar e Revisar</md-button><md-button ng-click=confirmar() class=\"md-raised md-primary\">Confirmar e Continuar</md-button></md-dialog-actions></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/modals/dialog-enviar-documentos-sei.html","<md-dialog aria-label=\"Imóvel atualizado com sucesso\" flex=60><form ng-cloak><md-toolbar><div class=md-toolbar-tools><h2>Operação realizada com sucesso</h2><span flex></span></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row><span>RIP do Imóvel: <span style=\"font-weight: bold\">{{imovel.rip}}</span><p></p></span></div><div layout=row ng-show=\"imovel.tipoProcessoSei === \'processoExistenteNaoSPU\'\"><p>Nº do Processo não existente no SEI da SPU: <span style=\"font-weight: bold\">{{imovel.numeroProcesso}}</span></p></div><div layout=row ng-show=\"imovel.tipoProcessoSei === \'processoExistente\' || imovel.tipoProcessoSei === \'criarProcesso\'\"><p>Nº do Processo SEI: <span style=\"font-weight: bold\">{{formatarNumero(imovel.numeroProcesso)}}</span></p></div><!--                 <div layout=\"row\">\n                    <p> Selecione abaixo os documentos a serem enviados para o SEI:\n                    </p>\n                </div>\n                <div style=\"margin-left: 20px\">\n                    <div layout=\"row\">\n                        <md-checkbox ng-model=\"envioSei.despachoConclusao\" name=\"despachoConclusao\"\n                                     aria-label=\"Despacho de conclusão - SPUnet - Cadastro de Imóveis\">\n                            Despacho de conclusão - SPUnet - Cadastro de Imóveis\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\">\n                        <md-checkbox ng-model=\"envioSei.folhaRosto\" name=\"folhaRosto\"\n                                     aria-label=\"Folha de Rosto do Imóvel\">\n                            Folha de Rosto do Imóvel\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\">\n                        <md-checkbox ng-model=\"envioSei.memorialDescritivo\" name=\"memorialDescritivo\"\n                                     aria-label=\"Memorial Descritivo do Imóvel\">\n                            Memorial Descritivo do Imóvel\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\" ng-show=\"imovel.terreno.arquivoCartorial.nome\">\n                        <md-checkbox ng-model=\"envioSei.shapefileTerrenoCartorial\" name=\"shapefileTerrenoCartorial\"\n                                     aria-label=\"Shapefile do Terreno Cartorial\">\n                            Shapefile do Terreno Cartorial\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\" ng-show=\"imovel.terreno.arquivoCadastral.nome\">\n                        <md-checkbox ng-model=\"envioSei.shapefileTerrenoCadastral\" name=\"shapefileTerrenoCadastral\"\n                                     aria-label=\"Shapefile do Terreno Cadastral\">\n                            Shapefile do Terreno Cadastral\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\" ng-show=\"showShapeFileBenfeitoria()\">\n                        <md-checkbox ng-model=\"envioSei.shapefileBenfeitorias\" name=\"shapefileBenfeitorias\"\n                                     aria-label=\"Shapefile da áreia das benfeitorias\">\n                            Shapefile das Áreas das benfeitorias\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\" ng-show=\"imovel.unidadeAutonoma.edificacao.arquivoGeometria.nomeOriginal\">\n                        <md-checkbox ng-model=\"envioSei.shapefileEdificacao\" name=\"shapefileEdificacao\"\n                                     aria-label=\"Shapefile da Edificação\">\n                            Shapefile da Edificação\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\" ng-show=\"imovel.titulos.length > 0\">\n                        <md-checkbox ng-model=\"envioSei.tituloAquisitivo\" name=\"tituloAquisitivo\"\n                                     aria-label=\"Títulos Aquisitivos\">\n                            Títulos Aquisitivos\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\" ng-show=\"imovel.incorporacao.certidoesCartoriais.length > 0\">\n                        <md-checkbox ng-model=\"envioSei.documentoCartorial\" name=\"documentoCartorial\"\n                                     aria-label=\"Documentos Cartoriais\">\n                            Documentos Cartoriais\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\" ng-show=\"imovel.arquivosComplementaresLista.length > 0\">\n                        <md-checkbox ng-model=\"envioSei.arquivosComplementares\" name=\"arquivosComplementares\"\n                                     aria-label=\"Arquivos Complementares do Terreno\">\n                            Arquivos Complementares do Terreno\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\" ng-show=\"showEdificacaoBenfeitoria || showEdificacaoAutonoma\">\n                        <md-checkbox ng-model=\"envioSei.documentacaoEdificacao\" name=\"documentacaoEdificacao\"\n                                     aria-label=\"Documentação da Edificação\">\n                            Documentação da Edificação\n                        </md-checkbox>\n                    </div>\n\n                    <div layout=\"row\" ng-show=\"showPlantaBenfeitoria || showPlantaAutonoma\">\n                        <md-checkbox ng-model=\"envioSei.arquivoPlanta\" name=\"arquivoPlanta\"\n                                     aria-label=\"Arquivos de Planta\">\n                            Arquivos de Planta\n                        </md-checkbox>\n                    </div>\n                </div> --></div></md-dialog-content><md-dialog-actions layout=row><md-button layout-align=right class=\"md-raised md-primary\" ng-click=fechar()>Fechar</md-button><!--             <md-button layout-align=\"right\" class=\"md-raised md-primary\" ng-click=\"confirmar()\">\n                Enviar os Documentos Selecionados\n            </md-button> --></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/modals/dialog-motivo-alteracao.html","<md-dialog aria-label=\"Motivo da Alteração\" flex=60 ng-cloak><ng-form name=motivoAlteracao><md-toolbar><div class=md-toolbar-tools><h2>Motivo da Alteração</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div><p ng-if=!!isValidarCadastroSpiunet>Esse imóvel possuí pendência na validação dos seus dados, pois é proveniente do SPIUnet. Ao continuar com a alteração, você concorda que as pendências foram sanadas.</p><p>Alterações realizadas nos dados dos imóveis podem impactar contratos de destinação, avaliações de imóveis e na contabilidade da SPU. Infome abaixo o motivo da alteração dos dados.</p></div><md-input-container ng-class=\"motivoAlteracao.descricao.$error.required ? \'md-input-invalid\' : null\" class=md-block flex><label>Motivo</label><textarea matinput ng-model=imovel.motivoAlteracao type=text required name=descricao maxlength=2000></textarea><span class=form-error ng-show=\"motivoAlteracao.descricao.$error.required && submitted\">Campo de preenchimento obrigatório</span></md-input-container><div layout=row><md-checkbox ng-model=imovel.cienciaMotivoAlteracao name=declaro required aria-label=\"Declaro que foi feita a revisão de todos os dados do imóvel e que as informações estão corretas\">Declaro que foi feita a revisão de todos os dados do imóvel e que as informações estão corretas.</md-checkbox><span class=form-error ng-show=\"motivoAlteracao.declaro.$error.required && submitted\">Campo de preenchimento obrigatório</span></div><div layout-gt-sm=row layout-margin layout-align=\"end right\"><md-button layout-align=right class=\"md-raised md-primary\" ng-click=fechar()>Voltar</md-button><md-button layout-align=right class=\"md-raised md-primary\" ng-click=confirmar()>Confirmar</md-button></div></div></md-dialog-content></ng-form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/modals/dialog-nao-pode-editar-imovel.html","<md-dialog aria-label=\"O imóvel não pode ser alterado.\" flex=50 ng-cloak><md-toolbar><div class=md-toolbar-tools><h2>O imóvel não pode ser alterado.</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=column layout-padding><p style=\"font-size: 16px; margin-bottom: 20px\">Este imóvel possui utilizações ativas. Por essa razão, somente sua UG Síndica poderá ser alterada.</p><p style=\"font-size: 16px; margin-bottom: 20px\">Selecione a nova UG Síndica:</p><md-input-container class=md-block flex=16 ng-if=ugSindica><label>UG Síndica:</label><md-autocomplete md-clear-button=false name=codigoUgSindica md-selected-item-change=setUgSindica(ug.codigo) md-selected-item=ugSindica md-search-text=searchUG md-items=\"ug in filtroUgs(searchUG)\" md-item-text=ug.display md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\"><md-item-template><span md-highlight-text=searchUG md-highlight-flags=^i>{{ug.codigo}} - {{ug.nome}}</span></md-item-template></md-autocomplete></md-input-container></div></div></md-dialog-content><md-dialog-actions layout=row><md-button ng-click=fechar() class=md-raised>Cancelar</md-button><md-button ng-click=confirmar() class=\"md-raised md-primary\">Salvar</md-button></md-dialog-actions></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/modals/dialog-processoSei.html","<md-dialog aria-label=\"\" flex=40><form ng-cloak><md-toolbar><div class=md-toolbar-tools><h4>Processo SEI na SPU.</h4><span flex></span></div></md-toolbar><br><label layout-margin layout-padding>Número de Processo não pertence ao SEI da SPU. Logo, neste caso, não haverá integração entre o SPUnet e o SEI. Isto não impede o cadastro do imóvel.</label><md-dialog-actions layout=row><md-button layout-align=right class=\"md-raised md-primary\" ng-click=fecharModal()>OK</md-button></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/modals/dialog-upload-arquivo.html","<md-dialog aria-label=\"Upload de Imagens\" flex=60 ng-cloak><ng-form name=uploadArquivo><md-toolbar><div class=md-toolbar-tools><h2>Upload de Arquivo</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin ng-show=\"arquivo.nomeArquivo == undefined\"><md-input-container ng-class=\"uploadArquivo.data.$error.required && submitted ? \'md-input-invalid\' :null\" class=md-block flex-gt-sm><label>Descrição</label><input ng-model=arquivo.descricao type=text required name=descricao> <span class=form-error ng-show=\"uploadArquivo.descricao.$error.required && submitted\">Campo de preenchimento obrigatório</span></md-input-container><div layout-gt-sm=column><md-datepicker ng-mask=39/19/9999 restrict=reject ng-model=arquivo.data md-placeholder=\"Data do arquivo\" required md-max-date=arquivoController.hoje name=data></md-datepicker><span class=form-error ng-show=\"uploadArquivo.data.$error.required && submitted\">Campo de preenchimento obrigatório</span></div></div><div layout-gt-sm=row layout-margin ng-show=!arquivo.nomeArquivo><input type=file ngf-select ng-model=arquivo.arquivo accept=\".pdf, .doc, .docx\" ngf-max-size=5MB ngf-model-invalid=errorFile required name=arquivoArq> <span class=form-error ng-show=\"uploadArquivo.arquivoArq.$error.required && submitted\">Campo de preenchimento obrigatório</span><md-button ng-click=incluir() layout-align=right class=\"md-primary md-raised\">Inserir</md-button></div><div layout=row ng-show=arquivo.nomeArquivo><md-content style=\"width: 100% !important\"><h4>Contrato de Compra</h4><table class=mui-table style=\"width:100% !important\"><thead><tr><th style=\"width: 200px !important\">Nome do contrato</th><th>Data de emissão</th><th>Nome do arquivo</th><th>Ação</th></tr></thead><tbody><tr ng-model-options=\"{trackBy: \'$value.id\'}\"><td>{{arquivo.descricao}}</td><td>{{arquivo.data | date : \'dd/MM/yyyy\'}}</td><td>{{arquivo.nomeArquivo}}</td><td><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=removerArquivo()></ng-md-icon></td></tr></tbody></table></md-content></div><div layout-gt-sm=row layout-margin layout-align=\"end right\"><md-button layout-align=right class=\"md-raised md-primary\" ng-click=confirmar()>Confirmar</md-button></div></div></md-dialog-content></ng-form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/modals/progress.tmpl.html","<div style=padding:7px><p>Por favor aguarde, carregando...</p><p><md-progress-linear md-mode=indeterminate></md-progress-linear></p></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/pesquisar-imovel/filtro-avancado.tmpl.html","<div layout=column flex><div layout=row><fieldset flex><legend>Filtros de Pesquisa</legend><div layout=row><md-input-container flex-gt-sm><label>Valor</label><input ng-model=valor name=valor></md-input-container><md-input-container flex-gt-sm><label>Grupo</label><md-select ng-model=grupo name=grupo><md-option ng-repeat=\"item in grupo\" ng-value=item>Grupo</md-option></md-select></md-input-container><md-input-container flex-gt-sm><label>Campos</label><md-select ng-model=campos name=campos><md-option ng-repeat=\"item in campos\" ng-value=item>Campos</md-option></md-select></md-input-container><md-input-container flex-gt-sm><md-select ng-model=tipo name=tipo><md-option ng-repeat=\"item in tipo\" ng-value=item>Tipo</md-option></md-select></md-input-container><md-button class=\"md-raised md-primary\" ng-click=adicionar()>Adicionar filtro</md-button></div><div layout=row><h2>Filtrando por:</h2></div></fieldset></div><div layout=row><a ui-sref=pesquisarImovel.pesquisaSimples>Pesquisa Simples</a></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/pesquisar-imovel/filtro-simples.tmpl.html","<div layout=column flex><h1 class=margem-horizontal>Pesquisar Imóvel</h1><md-divider class=margem-horizontal></md-divider><div class=\"tamanho-container primeiro-container margem-horizontal\" layout=row><md-input-container class=\"md-block margem-baixa-padrao\" flex=50><label>RIP</label><input campo-ajuda=INC_FORM_PESQ_IMOVEL_RIP name=rip ng-model=piCtrl.filtroImovel.rip></md-input-container><md-input-container ng-class=\"forms.pesquisaForm.pais.$error.required?\'md-input-invalid\':null\" class=md-block flex=50><label>País</label><md-select campo-ajuda=INC_FORM_PESQ_IMOVEL_PAIS ng-model=piCtrl.filtroImovel.enderecamento.pais name=pais ng-change=piCtrl.buscarCidadesPorPais() ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=true><md-option ng-repeat=\"item in piCtrl.paises | orderByBr:\'descricao\'\" ng-value=item ng-selected=\"item.id == 1\">{{item.descricao}}</md-option></md-select></md-input-container></div><div class=\"tamanho-container margem-horizontal\" layout=row><div layout=row layout-align=\"start center\" class=margem-baixa-padrao flex>Período de Cadastramento:<md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_PESQ_IMOVEL_DT_INICIO name=periodoInicio ng-model=piCtrl.filtroImovel.periodoCadastramentoInicio></md-datepicker><span style=\"margin-left: 18px\">a</span><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_PESQ_IMOVEL_DT_FIM name=periodoFim ng-model=piCtrl.filtroImovel.periodoCadastramentoFim></md-datepicker></div><md-input-container class=md-block ng-show=\"piCtrl.filtroImovel.enderecamento.pais.id == 1\" flex-gt-sm><label id=labelUf>UF</label><!--\n            <md-select campo-ajuda=\"INC_FORM_PESQ_IMOVEL_UF\" ng-model=\"piCtrl.filtroImovel.enderecamento.uf\" name=\"uf\" ng-change=\"piCtrl.buscarMunicipiosPorUf()\">\n                <md-option ng-repeat=\"item in piCtrl.ufs\" ng-value=\"item.sigla\">\n                    {{item.descricao}}\n                </md-option>\n            </md-select>\n        --><md-autocomplete campo-ajuda=INC_FORM_PESQ_IMOVEL_UF md-selected-item=piCtrl.filtroImovel.enderecamento.uf name=uf md-selected-item-change=piCtrl.buscarMunicipiosPorUf() md-clear-button=!$mdAutocompleteCtrl.isDisabled md-search-text=$scope.searchUf md-items=\"item in piCtrl.ufs | filter:{descricao:$scope.searchUf} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=labelUf><md-item-template><span md-highlight-text=$scope.searchUf md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchUf}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block flex-gt-sm ng-show=\"piCtrl.filtroImovel.enderecamento.pais.id != 1\"><label>Cidade exterior</label><md-select campo-ajuda=INC_FORM_PESQ_IMOVEL_CID_EXTER ng-model=piCtrl.filtroImovel.enderecamento.cidadeExterior name=cidadeExterior><md-option ng-repeat=\"item in piCtrl.cidades\" ng-value=item>{{item}}</md-option></md-select></md-input-container></div><div class=\"tamanho-container margem-horizontal\" layout=row><md-input-container class=\"md-block margem-baixa-padrao\" ng-show=\"piCtrl.filtroImovel.enderecamento.pais.id == 1\" flex=50><label id=labelMunicipio>Município</label><!--\n            <md-select campo-ajuda=\"INC_FORM_PESQ_IMOVEL_MUNICIPIO\" ng-model=\"piCtrl.filtroImovel.enderecamento.municipio\" name=\"municipio\" ng-disabled=\"piCtrl.municipios.length == 0\">\n                <md-option ng-repeat=\"item in piCtrl.municipios\" ng-value=\"item.nome\">\n                    {{item.nome}}\n                </md-option>\n            </md-select>\n        --><md-autocomplete campo-ajuda=INC_FORM_PESQ_IMOVEL_MUNICIPIO md-selected-item=imovel.sistemaReferencia name=municipio ng-disabled=\"piCtrl.municipios.length == 0\" md-clear-button=!$mdAutocompleteCtrl.isDisabled md-search-text=$scope.searchMunicipio md-items=\"item in piCtrl.municipios | filter:{nome:$scope.searchMunicipio} | orderByBr:\'nome\'\" md-item-text=item.nome md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=labelMunicipio><md-item-template><span md-highlight-text=$scope.searchMunicipio md-highlight-flags=^i>{{item.nome}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchMunicipio}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block flex=100><label>Bairro/Distrito</label><input campo-ajuda=INC_FORM_PESQ_IMOVEL_BAIRRO name=bairro ng-model=piCtrl.filtroImovel.enderecamento.bairro></md-input-container></div><div layout=row class=margem-horizontal-botoes layout-align=\"end stretch\"><md-button class=\"md-raised md-primary primeira-maiuscula\" ui-sref=pesquisarImovel.pesquisaAvancada>Pesquisa Avançada</md-button></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/pesquisar-imovel/lista-imoveis.tmpl.html","<div class=tabela-pesquisa-imovel><md-table-container><table md-table md-progress=piCtrl.promise ng-model=piCtrl.imovelSelecionado><thead md-head><tr md-row><th md-column>Imóvel</th><th md-column>Detalhes</th><th md-column>Ação</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in piCtrl.resultadoPesquisa\"><td md-cell style=\"text-align: center\"><p><img ng-src=\"{{item.imagens[0].imagem.arquivo != undefined ? \'data:image/jpeg;base64,\'+item.imagens[0].imagem.arquivo :\'./assets/img/no-image.png\'}}\"></p></td><td md-cell><p><span><span style=\"font-weight: bold\">RIP:</span> {{item.ripValido ? item.rip : \'-\' }}</span> <span style=\"font-weight: bold; margin-left: 10px\">Data de cadastramento:</span> {{item.dataCadastro | date : \'dd/MM/yyyy\'}} <span style=\"font-weight: bold; margin-left: 10px\">Matrícula/Trascrição:</span> {{item.incorporacao.matriculas.length > 0 ? item.matriculas : \'Não informado\'}}</p><p ng-show=\"item.enderecamento.pais.descricao == \'Brasil\'\"><span style=\"font-weight: bold\">Endereço:</span> {{item.enderecamento.tipoLogradouro}} {{item.enderecamento.logradouro}}, Número {{item.numero}}, {{item.enderecamento.bairro}}, {{item.enderecamento.municipio}}/{{item.enderecamento.uf}}-{{item.enderecamento.pais.descricao}}. CEP: {{item.enderecamento.cep}}</p><p><span style=\"font-weight: bold\">Proprietário/Titular:</span> {{item.proprietario.tipoProprietarioReal.descricao}}</p></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=piCtrl.showCancelarImovel($event,item,$index) class=\"md-fab md-raised md-mini\" aria-label=\"Cancelar Imóvel\" exibir-elemento-por-permissao=CAD_IMOVEL.CANCELAR_IMOVEL ng-if=piCtrl.exibeCancelar(item)><md-icon ng-bind=\"\'close\'\"><md-tooltip>Cancelar Imóvel</md-tooltip></md-icon></md-button><md-button ng-click=piCtrl.irParaEdicao(item.imovelAlterado.id) class=\"md-fab md-raised md-mini\" aria-label=\"Editar Imóvel\" exibir-elemento-por-permissao=CAD_IMOVEL.CADASTRAR_IMOVEL ng-if=piCtrl.exibeEditar(item)><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Imóvel</md-tooltip></md-icon></md-button><md-button ng-click=piCtrl.irParaDetalhamento(item.imovelAlterado.id) class=\"md-fab md-raised md-mini\" aria-label=\"Detalhar Imóvel\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Detalhar Imóvel</md-tooltip></md-icon></md-button><md-button ng-click=\"piCtrl.showLocalizacaoImovelDialog($event, piCtrl.buscarImovelMapa(item))\" class=\"md-fab md-raised md-mini\" aria-label=\"Ver Localização\"><md-icon ng-bind=\"\'map\'\"><md-tooltip>Ver Localização</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=piCtrl.paginacao.limit md-limit-options=piCtrl.paginacao.limitOptions md-page=piCtrl.paginacao.page md-total={{piCtrl.paginacao.totalElements}} md-on-paginate=piCtrl.page md-page-select ng-disabled=\"piCtrl.paginacao.totalElements < 6\"></md-table-pagination><div layout=row layout-align=\"end stretch\"><md-button class=\"md-raised md-primary\" ng-click=\"piCtrl.exportarPesquisa(\'pdf\')\">Exportar em PDF</md-button><md-button class=\"md-raised md-primary\" ng-click=\"piCtrl.exportarPesquisa(\'xls\')\">Exportar em Excel</md-button></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/detalhar-imovel-dados-localizacao-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-show=\"homCtrl.imovelOriginal.tipoImovel !== null\"><label class=label-titulo>Tipo do Imóvel:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.tipoImovel.descricao }}</span><br></span></td><td md-cell><div id=tipoImovel><span ng-show=\"homCtrl.imovelAlterado.tipoImovel !== null\"><label class=label-titulo>Tipo do Imóvel:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.tipoImovel.descricao, homCtrl.imovelAlterado.tipoImovel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.tipoImovel.descricao }}</span><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Tipo Imóvel\" name=tipoImovel ng-model=homCtrl.informacoesRecusada.tipoImovel ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.TIPO_IMOVEL)></md-checkbox></td></tr><tr md-row><td md-cell colspan=\"{{ homCtrl.sizeColspan() }}\"><div class=titulo-vpane-bottom-top>Geolocalização do Imóvel</div><inc-mapa-homologar-imovel-detalhar showminimapa=false imovel=homCtrl.imovelOriginal tabindex=-1 habilitar-desenho=true geometria=homCtrl.imovelAlterado edificacoes=homCtrl.todosImoveisDaUf></inc-mapa-homologar-imovel-detalhar><div class=legendas-mapa><b style=\"margin-top: 4px\">LEGENDA: </b>&emsp;<div id=retangulo1 style=\"margin-left: 50px\"></div>&ensp;<label style=\"margin-top: 4px\">Informação Original</label><div id=retangulo2 style=\"margin-left: 50px\"></div>&ensp;<label style=\"margin-top: 4px\">Informação Nova/Alterada&emsp;</label><div id=retangulo3 style=\"margin-left: 50px\"></div>&ensp;<label style=\"margin-top: 4px\">Outros Imóveis&emsp;</label></div><div id=mapaImovel ng-hide=true><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.pontoImovel.coordinates[0], homCtrl.imovelAlterado.pontoImovel.coordinates[0]) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelOriginal.pontoImovel.coordinates[0] }}</span> <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.pontoImovel.coordinates[1], homCtrl.imovelAlterado.pontoImovel.coordinates[1]) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelOriginal.pontoImovel.coordinates[1] }}</span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Mapa do Imóvel\" name=mapaImovel ng-model=homCtrl.informacoesRecusada.mapaImovel ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.MAPA_IMOVEL)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Coordenadas do Ponto</label><br><span ng-show=\"homCtrl.imovelOriginal.sistemaReferencia !== null\"><label class=label-component>Sistema de Referência:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.sistemaReferencia.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.pontoImovel !== null\"><label class=label-component>Graus Decimais:</label><span class=span-original-component>{{ homCtrl.coordenadasImovelOriginal.latitude.decimal }} Latitude {{ homCtrl.coordenadasImovelOriginal.latitude.hemisferio == \"S\" ? \"Sul\" : \"Norte\" }}</span><br><span class=span-original-component>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {{ homCtrl.coordenadasImovelOriginal.longitude.decimal }} Longitude {{ homCtrl.coordenadasImovelOriginal.longitude.hemisferio == \"O\" ? \"Oeste\" : \"Leste\" }}</span><br></span></td><td md-cell><div id=coordenadasPonto><label class=label-titulo>Coordenadas do Ponto</label><br><span ng-show=\"homCtrl.imovelAlteradol.sistemaReferencia !== null\"><label class=label-component>Sistema de Referência:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.sistemaReferencia.descricao, homCtrl.imovelAlterado.sistemaReferencia.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.sistemaReferencia.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.pontoImovel !== null\"><label class=label-component>Graus Decimais:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.pontoImovel.coordinates[0], homCtrl.imovelAlterado.pontoImovel.coordinates[0]) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.decimal }} Latitude {{ homCtrl.coordenadasImovelAlterado.latitude.hemisferio == \"S\" ? \"Sul\" : \"Norte\" }}</span><br><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.pontoImovel.coordinates[1], homCtrl.imovelAlterado.pontoImovel.coordinates[1]) ? \'span-alterado-component\' : \'span-original-component\'\">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {{ homCtrl.coordenadasImovelAlterado.longitude.decimal }} Longitude {{ homCtrl.coordenadasImovelAlterado.longitude.hemisferio == \"O\" ? \"Oeste\" : \"Leste\" }}</span><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Coordenadas do Ponto\" name=coordenadasPonto ng-model=homCtrl.informacoesRecusada.coordenadasPonto ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.COORDENADAS_PONTO)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-show=\"homCtrl.imovelOriginal.enderecamento !== null\"><label class=label-component>Endereço:</label><span class=span-original-component>{{ homCtrl.formAdress(homCtrl.imovelOriginal) }}</span><br></span></td><td md-cell><div id=endereco><span ng-show=\"homCtrl.imovelAlterado.enderecamento !== null\"><label class=label-component>Endereço:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.formAdress(homCtrl.imovelOriginal), homCtrl.formAdress(homCtrl.imovelAlterado)) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.formAdress(homCtrl.imovelAlterado) }}</span><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Endereco\" name=endereco ng-model=homCtrl.informacoesRecusada.endereco ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.ENDERECO)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/detalhar-imovel-dados-localizacao.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Localização do Imóvel</th></tr><tr md-row><th md-column width=45% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=45%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><inc-mapa-homologar geometria=homCtrl.imovelOriginal.pontoImovel></inc-mapa-homologar></td><td md-cell><inc-mapa-homologar geometria=homCtrl.imovelAlterado.pontoImovel dado-alterado=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.pontoImovel.coordinates, homCtrl.imovelAlterado.pontoImovel.coordinates)\"></inc-mapa-homologar></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar ponto do imóvel\" name=pontoImovel ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.pontoImovel.coordinates, homCtrl.imovelAlterado.pontoImovel.coordinates)\" ng-checked=\"homCtrl.exists(\'pontoImovel\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Coordenadas do Ponto</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.sistemaReferencia.descricao != null\"><td md-cell>Sistema de Referência</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.sistemaReferencia.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.sistemaReferencia.descricao, homCtrl.imovelAlterado.sistemaReferencia.descricao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.sistemaReferencia.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar sistema de referência\" name=sistemaReferencia ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.sistemaReferencia.descricao, homCtrl.imovelAlterado.sistemaReferencia.descricao)\" ng-checked=\"homCtrl.exists(\'sistemaReferencia\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.formatoCoordenadas != null\"><td md-cell>Formato das Coordenadas</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.formatoCoordenadas }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.formatoCoordenadas, homCtrl.imovelAlterado.formatoCoordenadas) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.formatoCoordenadas }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar formato das coordenadas\" name=formatoCoordenadas ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.formatoCoordenadas, homCtrl.imovelAlterado.formatoCoordenadas)\" ng-checked=\"homCtrl.exists(\'formatoCoordenadas\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.latitude.hemisferio != null\"><td md-cell>Latitude</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.latitude.hemisferio }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.latitude.hemisferio, homCtrl.coordenadasImovelAlterado.latitude.hemisferio) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.hemisferio }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar hemisféfio da latitude\" name=hemisferioLatitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.latitude.hemisferio, homCtrl.coordenadasImovelAlterado.latitude.hemisferio)\" ng-checked=\"homCtrl.exists(\'hemisferioLatitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.latitude.graus != null\"><td md-cell>Graus</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.latitude.graus }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.latitude.graus, homCtrl.coordenadasImovelAlterado.latitude.graus) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.graus }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar graus da latitude\" name=grausLatitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.latitude.graus, homCtrl.coordenadasImovelAlterado.latitude.graus)\" ng-checked=\"homCtrl.exists(\'grausLatitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.latitude.minutos != null\"><td md-cell>Minutos</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.latitude.minutos }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.latitude.minutos, homCtrl.coordenadasImovelAlterado.latitude.minutos) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.minutos }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar minutos da latitude\" name=minutosLatitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.latitude.minutos, homCtrl.coordenadasImovelAlterado.latitude.minutos)\" ng-checked=\"homCtrl.exists(\'minutosLatitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.latitude.segundos != null\"><td md-cell>Segundos</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.latitude.segundos | number:2 }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.latitude.segundos, homCtrl.coordenadasImovelAlterado.latitude.segundos) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.segundos | number:2 }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar segundos da latitude\" name=segundosLatitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.latitude.segundos, homCtrl.coordenadasImovelAlterado.latitude.segundos)\" ng-checked=\"homCtrl.exists(\'segundosLatitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.latitude.decimal != null\"><td md-cell>Decimal</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.latitude.decimal }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.latitude.decimal, homCtrl.coordenadasImovelAlterado.latitude.decimal) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.decimal }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar decimal da latitude\" name=decimalLatitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.latitude.decimal, homCtrl.coordenadasImovelAlterado.latitude.decimal)\" ng-checked=\"homCtrl.exists(\'decimalLatitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.longitude.hemisferio != null\"><td md-cell>Longitude</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.longitude.hemisferio }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.longitude.hemisferio, homCtrl.coordenadasImovelAlterado.longitude.hemisferio) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.longitude.hemisferio }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar hemisferio da longitude\" name=hemisferioLongitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.longitude.hemisferio, homCtrl.coordenadasImovelAlterado.longitude.hemisferio)\" ng-checked=\"homCtrl.exists(\'hemisferioLongitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.longitude.graus != null\"><td md-cell>Graus</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.longitude.graus }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.longitude.graus, homCtrl.coordenadasImovelAlterado.longitude.graus) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.longitude.graus }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar graus da longitude\" name=grausLongitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.longitude.graus, homCtrl.coordenadasImovelAlterado.longitude.graus)\" ng-checked=\"homCtrl.exists(\'grausLongitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.longitude.minutos != null\"><td md-cell>Minutos</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.longitude.minutos }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.longitude.minutos, homCtrl.coordenadasImovelAlterado.longitude.minutos) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.longitude.minutos }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar minutos da longitude\" name=minutosLongitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.longitude.minutos, homCtrl.coordenadasImovelAlterado.longitude.minutos)\" ng-checked=\"homCtrl.exists(\'minutosLongitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.longitude.segundos != null\"><td md-cell>Segundos</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.longitude.segundos | number:2 }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.longitude.segundos, homCtrl.coordenadasImovelAlterado.longitude.segundos) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.longitude.segundos | number:2 }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar segundos da longitude\" name=segundosLongitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.longitude.segundos, homCtrl.coordenadasImovelAlterado.longitude.segundos)\" ng-checked=\"homCtrl.exists(\'segundosLongitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.longitude.decimal != null\"><td md-cell>Decimal</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.longitude.decimal }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.longitude.decimal, homCtrl.coordenadasImovelAlterado.longitude.decimal) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.longitude.decimal }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar decimal da longitude\" name=decimalLongitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.longitude.decimal, homCtrl.coordenadasImovelAlterado.longitude.decimal)\" ng-checked=\"homCtrl.exists(\'decimalLongitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.coordenadaE != null\"><td md-cell>Coordenada E</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.coordenadaE }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.coordenadaE, homCtrl.coordenadasImovelAlterado.coordenadaE) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.coordenadaE }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar coordenada E\" name=coordenadaE ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.coordenadaE, homCtrl.coordenadasImovelAlterado.coordenadaE)\" ng-checked=\"homCtrl.exists(\'coordenadaE\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.coordenadaN != null\"><td md-cell>Coordenada N</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.coordenadaN }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.coordenadaN, homCtrl.coordenadasImovelAlterado.coordenadaN) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.coordenadaN }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar coordenada N\" name=coordenadaN ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.coordenadaN, homCtrl.coordenadasImovelAlterado.coordenadaN)\" ng-checked=\"homCtrl.exists(\'coordenadaN\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.fusoUTM.zona != null\"><td md-cell>Fuso UTM</td><td md-cell ng-show=homCtrl.isCadastroAlterado() ng-disabled=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.fusoUTM.zona + \" \" + homCtrl.coordenadasImovelOriginal.fusoUTM.hemisferio }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.fusoUTM, homCtrl.coordenadasImovelAlterado.fusoUTM) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.fusoUTM.zona + \" \" + homCtrl.coordenadasImovelAlterado.fusoUTM.hemisferio }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fuso UTM\" name=fusoUTM ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.fusoUTM, homCtrl.coordenadasImovelAlterado.fusoUTM)\" ng-checked=\"homCtrl.exists(\'fusoUTM\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Endereço do Imóvel</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.tipoImovel.descricao != null\"><td md-cell>Tipo do Imóvel</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.tipoImovel.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.tipoImovel.id, homCtrl.imovelAlterado.tipoImovel.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.tipoImovel.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo do imóvel\" name=tipoImovel ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.tipoImovel.id, homCtrl.imovelAlterado.tipoImovel.id)\" ng-checked=\"homCtrl.exists(\'tipoImovel\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.pais.descricao != null\"><td md-cell>País</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.pais.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.pais.id, homCtrl.imovelAlterado.enderecamento.pais.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.pais.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar país\" name=pais ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.pais.id, homCtrl.imovelAlterado.enderecamento.pais.id)\" ng-checked=\"homCtrl.exists(\'pais\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.cep != null\"><td md-cell>CEP</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.cep }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.cep, homCtrl.imovelAlterado.enderecamento.cep) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.cep }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar cep\" name=cep ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.cep, homCtrl.imovelAlterado.enderecamento.cep)\" ng-checked=\"homCtrl.exists(\'cep\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.uf != null\"><td md-cell>UF</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.uf }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.uf, homCtrl.imovelAlterado.enderecamento.uf) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.uf }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar UF\" name=uf ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.uf, homCtrl.imovelAlterado.enderecamento.uf)\" ng-checked=\"homCtrl.exists(\'uf\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.municipio != null\"><td md-cell>Município</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.municipio }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.municipio, homCtrl.imovelAlterado.enderecamento.municipio) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.municipio }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar município\" name=municipio ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.municipio, homCtrl.imovelAlterado.enderecamento.municipio)\" ng-checked=\"homCtrl.exists(\'municipio\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.tipoLogradouro != null\"><td md-cell>Tipo Logradouro</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.tipoLogradouro }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.tipoLogradouro, homCtrl.imovelAlterado.enderecamento.tipoLogradouro) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.tipoLogradouro }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de logradouro\" name=tipoLogradouro ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.tipoLogradouro, homCtrl.imovelAlterado.enderecamento.tipoLogradouro)\" ng-checked=\"homCtrl.exists(\'tipoLogradouro\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.logradouro != null\"><td md-cell>Logradouro</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.logradouro }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.logradouro, homCtrl.imovelAlterado.enderecamento.logradouro) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.logradouro }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar logradouro\" name=logradouro ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.logradouro, homCtrl.imovelAlterado.enderecamento.logradouro)\" ng-checked=\"homCtrl.exists(\'logradouro\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.numero != null\"><td md-cell>Nº</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.numero }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numero, homCtrl.imovelAlterado.numero) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.numero }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número\" name=numero ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.numero, homCtrl.imovelAlterado.numero)\" ng-checked=\"homCtrl.exists(\'numero\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.bairro != null\"><td md-cell>Bairro/Distrito</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.bairro }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.bairro, homCtrl.imovelAlterado.enderecamento.bairro) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.bairro }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar bairro\" name=bairro ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.bairro, homCtrl.imovelAlterado.enderecamento.bairro)\" ng-checked=\"homCtrl.exists(\'bairro\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"!homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.cidadeExterior != null\"><td md-cell>Cidade Exterior</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.cidadeExterior }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.cidadeExterior, homCtrl.imovelAlterado.enderecamento.cidadeExterior) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.cidadeExterior }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar cidade exterior\" name=cidadeExterior ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.cidadeExterior, homCtrl.imovelAlterado.enderecamento.cidadeExterior)\" ng-checked=\"homCtrl.exists(\'cidadeExterior\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"!homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.codigoPostal != null\"><td md-cell>Código Postal</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.codigoPostal }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.codigoPostal, homCtrl.imovelAlterado.enderecamento.codigoPostal) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.codigoPostal }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar código postal\" name=codigoPostal ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.codigoPostal, homCtrl.imovelAlterado.enderecamento.codigoPostal)\" ng-checked=\"homCtrl.exists(\'codigoPostal\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.complemento != null\"><td md-cell>Complemento</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.complemento }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.complemento, homCtrl.imovelAlterado.complemento) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.complemento }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar complemento\" name=complemento ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.complemento, homCtrl.imovelAlterado.complemento)\" ng-checked=\"homCtrl.exists(\'complemento\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"!homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.endereco != null\"><td md-cell>Endereço</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.endereco }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.endereco, homCtrl.imovelAlterado.enderecamento.endereco) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.endereco }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar endereço\" name=endereco ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.endereco, homCtrl.imovelAlterado.enderecamento.endereco)\" ng-checked=\"homCtrl.exists(\'endereco\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/dialog-recusar-imovel.html","<md-dialog aria-label=\"Informe Dados Rejeitados\" flex=40><form name=cancelarImovel ng-submit=incluir()><md-toolbar><div class=md-toolbar-tools><h2>Informe Dados Rejeitados</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content>É necessário selecionar a opção recusar de pelo menos um dos campos.</div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/mapa-homologar-imovel-alterado-benfeitoria.html","<div ng-controller=MapaAlteradoBenfeitoriaController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\"><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=overlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/mapa-homologar-imovel-alterado-terreno-cartorial.html","<div ng-controller=MapaAlteradoTerrenoCartorialController><div class=mapaImovel><openlayers class=mapaTerrenoAlterado ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\"><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=terrenoCartorialOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/mapa-homologar-imovel-alterado-terreno.html","<div ng-controller=MapaAlteradoTerrenoController><div class=mapaImovel><openlayers class=mapaTerrenoAlterado ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\"><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=terrenoCadastralOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/mapa-homologar-imovel-alterado-unidade-autonoma.html","<div ng-controller=MapaAlteradoUnidadeAutonomaController><div class=mapaImovel><openlayers class=mapaUnidadeAutonoimaAlterada ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\"><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=unidadeAutonomaOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/mapa-homologar-imovel-alterado.html","<div ng-controller=MapaAlteradoController><div class=mapaImovel><openlayers class=mapaHomologarAlterado ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\"><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=terrenoOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/mapa-homologar-imovel-atual-benfeitoria.html","<div ng-controller=MapaOriginalBenfeitoriaController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\" class=mapaBenfeitoriaImovel><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=benfeitoriaOriginalOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/mapa-homologar-imovel-atual-terreno-cartorial.html","<div ng-controller=MapaOriginalTerrenoCartorialController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\" class=mapaTerrenoImovel><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=terrenoCartorialOriginalOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/mapa-homologar-imovel-atual-terreno.html","<div ng-controller=MapaOriginalTerrenoController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\" class=mapaTerrenoImovel><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=terrenoOriginalOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/mapa-homologar-imovel-atual-unidade-autonoma.html","<div ng-controller=MapaOriginalUnidadeAutonomaController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\" class=mapaUnidadeAutonoma><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=unidadeAutonomaOriginalOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/mapa-homologar-imovel-atual.html","<div ng-controller=MapaOriginalController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\" class=mapaPontoImovel><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=mapaOriginalOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/detalhar-imovel-dados-localizacao-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-show=\"homCtrl.imovelOriginal.tipoImovel !== null\"><label class=label-titulo>Tipo do Imóvel:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.tipoImovel.descricao }}</span><br></span></td><td md-cell><div id=tipoImovel><span ng-show=\"homCtrl.imovelAlterado.tipoImovel !== null\"><label class=label-titulo>Tipo do Imóvel:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.tipoImovel.descricao, homCtrl.imovelAlterado.tipoImovel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.tipoImovel.descricao }}</span><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Tipo Imóvel\" name=tipoImovel ng-model=homCtrl.informacoesRecusada.tipoImovel ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.TIPO_IMOVEL)></md-checkbox></td></tr><tr md-row><td md-cell colspan=\"{{ homCtrl.sizeColspan() }}\"><div class=titulo-vpane-bottom-top>Geolocalização do Imóvel</div><inc-mapa-pendencia-imovel-detalhar showminimapa=false imovel=homCtrl.imovelOriginal tabindex=-1 habilitar-desenho=true geometria=homCtrl.imovelAlterado edificacoes=homCtrl.todosImoveisDaUf></inc-mapa-pendencia-imovel-detalhar><div class=legendas-mapa><b style=\"margin-top: 4px\">LEGENDA: </b>&emsp;<div id=retangulo1 style=\"margin-left: 50px\"></div>&ensp;<label style=\"margin-top: 4px\">Informação Original</label><div id=retangulo2 style=\"margin-left: 50px\"></div>&ensp;<label style=\"margin-top: 4px\">Informação Nova/Alterada&emsp;</label><div id=retangulo3 style=\"margin-left: 50px\"></div>&ensp;<label style=\"margin-top: 4px\">Outros Imóveis&emsp;</label></div><div id=mapaImovel ng-hide=true><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.pontoImovel.coordinates[0], homCtrl.imovelAlterado.pontoImovel.coordinates[0]) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelOriginal.pontoImovel.coordinates[0] }}</span> <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.pontoImovel.coordinates[1], homCtrl.imovelAlterado.pontoImovel.coordinates[1]) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelOriginal.pontoImovel.coordinates[1] }}</span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Mapa do Imóvel\" name=mapaImovel ng-model=homCtrl.informacoesRecusada.mapaImovel ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.MAPA_IMOVEL)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Coordenadas do Ponto</label><br><span ng-show=\"homCtrl.imovelOriginal.sistemaReferencia !== null\"><label class=label-component>Sistema de Referência:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.sistemaReferencia.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.pontoImovel !== null\"><label class=label-component>Graus Decimais:</label><span class=span-original-component>{{ homCtrl.coordenadasImovelOriginal.latitude.decimal }} Latitude {{ homCtrl.coordenadasImovelOriginal.latitude.hemisferio == \"S\" ? \"Sul\" : \"Norte\" }}</span><br><span class=span-original-component>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {{ homCtrl.coordenadasImovelOriginal.longitude.decimal }} Longitude {{ homCtrl.coordenadasImovelOriginal.longitude.hemisferio == \"O\" ? \"Oeste\" : \"Leste\" }}</span><br></span></td><td md-cell><div id=coordenadasPonto><label class=label-titulo>Coordenadas do Ponto</label><br><span ng-show=\"homCtrl.imovelAlteradol.sistemaReferencia !== null\"><label class=label-component>Sistema de Referência:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.sistemaReferencia.descricao, homCtrl.imovelAlterado.sistemaReferencia.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.sistemaReferencia.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.pontoImovel !== null\"><label class=label-component>Graus Decimais:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.pontoImovel.coordinates[0], homCtrl.imovelAlterado.pontoImovel.coordinates[0]) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.decimal }} Latitude {{ homCtrl.coordenadasImovelAlterado.latitude.hemisferio == \"S\" ? \"Sul\" : \"Norte\" }}</span><br><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.pontoImovel.coordinates[1], homCtrl.imovelAlterado.pontoImovel.coordinates[1]) ? \'span-alterado-component\' : \'span-original-component\'\">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {{ homCtrl.coordenadasImovelAlterado.longitude.decimal }} Longitude {{ homCtrl.coordenadasImovelAlterado.longitude.hemisferio == \"O\" ? \"Oeste\" : \"Leste\" }}</span><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Coordenadas do Ponto\" name=coordenadasPonto ng-model=homCtrl.informacoesRecusada.coordenadasPonto ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.COORDENADAS_PONTO)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-show=\"homCtrl.imovelOriginal.enderecamento !== null\"><label class=label-component>Endereço:</label><span class=span-original-component>{{ homCtrl.formAdress(homCtrl.imovelOriginal) }}</span><br></span></td><td md-cell><div id=endereco><span ng-show=\"homCtrl.imovelAlterado.enderecamento !== null\"><label class=label-component>Endereço:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.formAdress(homCtrl.imovelOriginal), homCtrl.formAdress(homCtrl.imovelAlterado)) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.formAdress(homCtrl.imovelAlterado) }}</span><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Endereco\" name=endereco ng-model=homCtrl.informacoesRecusada.endereco ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.ENDERECO)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/detalhar-imovel-dados-localizacao.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column class=table-title colspan=3>Localização do Imóvel</th></tr><tr md-row><th md-column width=45% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=45%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><inc-mapa-pendencia geometria=homCtrl.imovelOriginal.pontoImovel></inc-mapa-pendencia></td><td md-cell><inc-mapa-pendencia geometria=homCtrl.imovelAlterado.pontoImovel dado-alterado=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.pontoImovel.coordinates, homCtrl.imovelAlterado.pontoImovel.coordinates)\"></inc-mapa-pendencia></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar ponto do imóvel\" name=pontoImovel ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.pontoImovel.coordinates, homCtrl.imovelAlterado.pontoImovel.coordinates)\" ng-checked=\"homCtrl.exists(\'pontoImovel\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column class=table-title colspan=4>Coordenadas do Ponto</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.sistemaReferencia.descricao != null\"><td md-cell>Sistema de Referência</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.sistemaReferencia.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.sistemaReferencia.descricao, homCtrl.imovelAlterado.sistemaReferencia.descricao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.sistemaReferencia.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar sistema de referência\" name=sistemaReferencia ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.sistemaReferencia.descricao, homCtrl.imovelAlterado.sistemaReferencia.descricao)\" ng-checked=\"homCtrl.exists(\'sistemaReferencia\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.formatoCoordenadas != null\"><td md-cell>Formato das Coordenadas</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.formatoCoordenadas }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.formatoCoordenadas, homCtrl.imovelAlterado.formatoCoordenadas) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.formatoCoordenadas }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar formato das coordenadas\" name=formatoCoordenadas ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.formatoCoordenadas, homCtrl.imovelAlterado.formatoCoordenadas)\" ng-checked=\"homCtrl.exists(\'formatoCoordenadas\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.latitude.hemisferio != null\"><td md-cell>Latitude</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.latitude.hemisferio }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.latitude.hemisferio, homCtrl.coordenadasImovelAlterado.latitude.hemisferio) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.hemisferio }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar hemisféfio da latitude\" name=hemisferioLatitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.latitude.hemisferio, homCtrl.coordenadasImovelAlterado.latitude.hemisferio)\" ng-checked=\"homCtrl.exists(\'hemisferioLatitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.latitude.graus != null\"><td md-cell>Graus</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.latitude.graus }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.latitude.graus, homCtrl.coordenadasImovelAlterado.latitude.graus) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.graus }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar graus da latitude\" name=grausLatitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.latitude.graus, homCtrl.coordenadasImovelAlterado.latitude.graus)\" ng-checked=\"homCtrl.exists(\'grausLatitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.latitude.minutos != null\"><td md-cell>Minutos</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.latitude.minutos }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.latitude.minutos, homCtrl.coordenadasImovelAlterado.latitude.minutos) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.minutos }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar minutos da latitude\" name=minutosLatitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.latitude.minutos, homCtrl.coordenadasImovelAlterado.latitude.minutos)\" ng-checked=\"homCtrl.exists(\'minutosLatitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.latitude.segundos != null\"><td md-cell>Segundos</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.latitude.segundos | number:2 }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.latitude.segundos, homCtrl.coordenadasImovelAlterado.latitude.segundos) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.segundos | number:2 }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar segundos da latitude\" name=segundosLatitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.latitude.segundos, homCtrl.coordenadasImovelAlterado.latitude.segundos)\" ng-checked=\"homCtrl.exists(\'segundosLatitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.latitude.decimal != null\"><td md-cell>Decimal</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.latitude.decimal }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.latitude.decimal, homCtrl.coordenadasImovelAlterado.latitude.decimal) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.latitude.decimal }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar decimal da latitude\" name=decimalLatitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.latitude.decimal, homCtrl.coordenadasImovelAlterado.latitude.decimal)\" ng-checked=\"homCtrl.exists(\'decimalLatitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.longitude.hemisferio != null\"><td md-cell>Longitude</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.longitude.hemisferio }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.longitude.hemisferio, homCtrl.coordenadasImovelAlterado.longitude.hemisferio) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.longitude.hemisferio }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar hemisferio da longitude\" name=hemisferioLongitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.longitude.hemisferio, homCtrl.coordenadasImovelAlterado.longitude.hemisferio)\" ng-checked=\"homCtrl.exists(\'hemisferioLongitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.longitude.graus != null\"><td md-cell>Graus</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.longitude.graus }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.longitude.graus, homCtrl.coordenadasImovelAlterado.longitude.graus) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.longitude.graus }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar graus da longitude\" name=grausLongitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.longitude.graus, homCtrl.coordenadasImovelAlterado.longitude.graus)\" ng-checked=\"homCtrl.exists(\'grausLongitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.longitude.minutos != null\"><td md-cell>Minutos</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.longitude.minutos }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.longitude.minutos, homCtrl.coordenadasImovelAlterado.longitude.minutos) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.longitude.minutos }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar minutos da longitude\" name=minutosLongitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.longitude.minutos, homCtrl.coordenadasImovelAlterado.longitude.minutos)\" ng-checked=\"homCtrl.exists(\'minutosLongitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.longitude.segundos != null\"><td md-cell>Segundos</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.longitude.segundos | number:2 }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.longitude.segundos, homCtrl.coordenadasImovelAlterado.longitude.segundos) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.longitude.segundos | number:2 }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar segundos da longitude\" name=segundosLongitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.longitude.segundos, homCtrl.coordenadasImovelAlterado.longitude.segundos)\" ng-checked=\"homCtrl.exists(\'segundosLongitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.longitude.decimal != null\"><td md-cell>Decimal</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.longitude.decimal }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.longitude.decimal, homCtrl.coordenadasImovelAlterado.longitude.decimal) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.longitude.decimal }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar decimal da longitude\" name=decimalLongitude ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.longitude.decimal, homCtrl.coordenadasImovelAlterado.longitude.decimal)\" ng-checked=\"homCtrl.exists(\'decimalLongitude\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.coordenadaE != null\"><td md-cell>Coordenada E</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.coordenadaE }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.coordenadaE, homCtrl.coordenadasImovelAlterado.coordenadaE) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.coordenadaE }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar coordenada E\" name=coordenadaE ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.coordenadaE, homCtrl.coordenadasImovelAlterado.coordenadaE)\" ng-checked=\"homCtrl.exists(\'coordenadaE\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.coordenadaN != null\"><td md-cell>Coordenada N</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.coordenadaN }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.coordenadaN, homCtrl.coordenadasImovelAlterado.coordenadaN) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.coordenadaN }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar coordenada N\" name=coordenadaN ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.coordenadaN, homCtrl.coordenadasImovelAlterado.coordenadaN)\" ng-checked=\"homCtrl.exists(\'coordenadaN\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.coordenadasImovelAlterado.fusoUTM.zona != null\"><td md-cell>Fuso UTM</td><td md-cell ng-show=homCtrl.isCadastroAlterado() ng-disabled=homCtrl.isCadastroAlterado()><span>{{ homCtrl.coordenadasImovelOriginal.fusoUTM.zona + \" \" + homCtrl.coordenadasImovelOriginal.fusoUTM.hemisferio }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.coordenadasImovelOriginal.fusoUTM, homCtrl.coordenadasImovelAlterado.fusoUTM) ? \'dado-alterado\' : \'\'\">{{ homCtrl.coordenadasImovelAlterado.fusoUTM.zona + \" \" + homCtrl.coordenadasImovelAlterado.fusoUTM.hemisferio }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fuso UTM\" name=fusoUTM ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.coordenadasImovelOriginal.fusoUTM, homCtrl.coordenadasImovelAlterado.fusoUTM)\" ng-checked=\"homCtrl.exists(\'fusoUTM\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column class=table-title colspan=4>Endereço do Imóvel</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.tipoImovel.descricao != null\"><td md-cell>Tipo do Imóvel</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.tipoImovel.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.tipoImovel.id, homCtrl.imovelAlterado.tipoImovel.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.tipoImovel.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo do imóvel\" name=tipoImovel ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.tipoImovel.id, homCtrl.imovelAlterado.tipoImovel.id)\" ng-checked=\"homCtrl.exists(\'tipoImovel\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.pais.descricao != null\"><td md-cell>País</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.pais.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.pais.id, homCtrl.imovelAlterado.enderecamento.pais.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.pais.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar país\" name=pais ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.pais.id, homCtrl.imovelAlterado.enderecamento.pais.id)\" ng-checked=\"homCtrl.exists(\'pais\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.cep != null\"><td md-cell>CEP</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.cep }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.cep, homCtrl.imovelAlterado.enderecamento.cep) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.cep }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar cep\" name=cep ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.cep, homCtrl.imovelAlterado.enderecamento.cep)\" ng-checked=\"homCtrl.exists(\'cep\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.uf != null\"><td md-cell>UF</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.uf }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.uf, homCtrl.imovelAlterado.enderecamento.uf) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.uf }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar UF\" name=uf ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.uf, homCtrl.imovelAlterado.enderecamento.uf)\" ng-checked=\"homCtrl.exists(\'uf\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.municipio != null\"><td md-cell>Município</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.municipio }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.municipio, homCtrl.imovelAlterado.enderecamento.municipio) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.municipio }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar município\" name=municipio ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.municipio, homCtrl.imovelAlterado.enderecamento.municipio)\" ng-checked=\"homCtrl.exists(\'municipio\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.tipoLogradouro != null\"><td md-cell>Tipo Logradouro</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.tipoLogradouro }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.tipoLogradouro, homCtrl.imovelAlterado.enderecamento.tipoLogradouro) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.tipoLogradouro }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de logradouro\" name=tipoLogradouro ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.tipoLogradouro, homCtrl.imovelAlterado.enderecamento.tipoLogradouro)\" ng-checked=\"homCtrl.exists(\'tipoLogradouro\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.logradouro != null\"><td md-cell>Logradouro</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.logradouro }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.logradouro, homCtrl.imovelAlterado.enderecamento.logradouro) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.logradouro }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar logradouro\" name=logradouro ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.logradouro, homCtrl.imovelAlterado.enderecamento.logradouro)\" ng-checked=\"homCtrl.exists(\'logradouro\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.numero != null\"><td md-cell>Nº</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.numero }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numero, homCtrl.imovelAlterado.numero) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.numero }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número\" name=numero ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.numero, homCtrl.imovelAlterado.numero)\" ng-checked=\"homCtrl.exists(\'numero\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.bairro != null\"><td md-cell>Bairro/Distrito</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.bairro }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.bairro, homCtrl.imovelAlterado.enderecamento.bairro) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.bairro }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar bairro\" name=bairro ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.bairro, homCtrl.imovelAlterado.enderecamento.bairro)\" ng-checked=\"homCtrl.exists(\'bairro\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"!homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.cidadeExterior != null\"><td md-cell>Cidade Exterior</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.cidadeExterior }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.cidadeExterior, homCtrl.imovelAlterado.enderecamento.cidadeExterior) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.cidadeExterior }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar cidade exterior\" name=cidadeExterior ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.cidadeExterior, homCtrl.imovelAlterado.enderecamento.cidadeExterior)\" ng-checked=\"homCtrl.exists(\'cidadeExterior\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"!homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.codigoPostal != null\"><td md-cell>Código Postal</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.codigoPostal }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.codigoPostal, homCtrl.imovelAlterado.enderecamento.codigoPostal) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.codigoPostal }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar código postal\" name=codigoPostal ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.codigoPostal, homCtrl.imovelAlterado.enderecamento.codigoPostal)\" ng-checked=\"homCtrl.exists(\'codigoPostal\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.complemento != null\"><td md-cell>Complemento</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.complemento }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.complemento, homCtrl.imovelAlterado.complemento) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.complemento }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar complemento\" name=complemento ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.complemento, homCtrl.imovelAlterado.complemento)\" ng-checked=\"homCtrl.exists(\'complemento\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"!homCtrl.isPaisBrasil() && homCtrl.imovelAlterado.enderecamento.endereco != null\"><td md-cell>Endereço</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.enderecamento.endereco }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.enderecamento.endereco, homCtrl.imovelAlterado.enderecamento.endereco) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.enderecamento.endereco }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar endereço\" name=endereco ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.enderecamento.endereco, homCtrl.imovelAlterado.enderecamento.endereco)\" ng-checked=\"homCtrl.exists(\'endereco\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/dialog-recusar-imovel.html","<md-dialog aria-label=\"Informe Dados Rejeitados\" flex=40><form name=cancelarImovel ng-submit=incluir()><md-toolbar><div class=md-toolbar-tools><h2>Informe Dados Rejeitados</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content>É necessário selecionar a opção recusar de pelo menos um dos campos.</div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/mapa-pendencia-imovel-alterado-benfeitoria.html","<div ng-controller=MapaAlteradoBenfeitoriaController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\"><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=overlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/mapa-pendencia-imovel-alterado-terreno-cartorial.html","<div ng-controller=MapaAlteradoTerrenoCartorialController><div class=mapaImovel><openlayers class=mapaTerrenoAlterado ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\"><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=terrenoCartorialOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/mapa-pendencia-imovel-alterado-terreno.html","<div ng-controller=MapaAlteradoTerrenoController><div class=mapaImovel><openlayers class=mapaTerrenoAlterado ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\"><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=terrenoCadastralOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/mapa-pendencia-imovel-alterado-unidade-autonoma.html","<div ng-controller=MapaAlteradoUnidadeAutonomaController><div class=mapaImovel><openlayers class=mapaUnidadeAutonoimaAlterada ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\"><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=unidadeAutonomaOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/mapa-pendencia-imovel-alterado.html","<div ng-controller=MapaAlteradoController><div class=mapaImovel><openlayers class=mapaHomologarAlterado ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\"><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=terrenoOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/mapa-pendencia-imovel-atual-benfeitoria.html","<div ng-controller=MapaOriginalBenfeitoriaController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\" class=mapaBenfeitoriaImovel><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=benfeitoriaOriginalOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/mapa-pendencia-imovel-atual-terreno-cartorial.html","<div ng-controller=MapaOriginalTerrenoCartorialController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\" class=mapaTerrenoImovel><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=terrenoCartorialOriginalOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/mapa-pendencia-imovel-atual-terreno.html","<div ng-controller=MapaOriginalTerrenoController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\" class=mapaTerrenoImovel><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=terrenoOriginalOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/mapa-pendencia-imovel-atual-unidade-autonoma.html","<div ng-controller=MapaOriginalUnidadeAutonomaController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\" class=mapaUnidadeAutonoma><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=unidadeAutonomaOriginalOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/mapa-pendencia-imovel-atual.html","<div ng-controller=MapaOriginalController><div class=mapaImovel><openlayers ol-center=brasilia ol-defaults=defaults style=\"height: 300px; width: 500px !important\" class=mapaPontoImovel><ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control><div id=mapaOriginalOverlay class=overlay><div layout=row><img class=overlay-image src=data:image/jpeg;base64,{{imovel.imagens[0].imagem}}><div class=overlay-content id=popup-content><p><b>RIP:</b> {{imovel.rip}}</p><p><b>Endereço:</b> {{imovel.enderecamento.bairro}}, {{imovel.enderecamento.municipio}}</p><p><b>CEP:</b> {{imovel.enderecamento.cep}}</p></div></div></div></openlayers></div><span ng-init=init()></span></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/benfeitoria/inclusao-benfeitoria/inclusao-benfeitoria.html","<div layout=row layout-xs=column><div layout=column flex=30 style=\"padding: 0 10px\"><div layout=column><div class=titulo-agrupamento><h5>Localização da Acessão/Benfeitoria</h5></div><div class=div-cimapa ng-if=showInclusaoBenfeitoria><ci-mapa-incorporacao showminimapa=false imovel=imovel action=add tabindex=4 benfeitoria=benfeitoria habilitar-desenho=\"ctrl.tipoDesenhoBenfeitoria === 2\"></ci-mapa-incorporacao></div></div></div><div layout=column flex=70 flex-xs=100 style=\"padding: 0 10px\"><div ng-if=benfeitoria.id class=titulo-agrupamento><h5>ID da Benfeitoria: {{benfeitoria.id}}</h5></div><ng-form name=forms.inclusaoBenfeitoriaForm><v-accordion class=vAccordion--default multiple><md-input-container flex style=\"width: 100%\"><label>Tipo de Acessão/Benfeitoria</label><md-select campo-ajuda=INC_FORM_BENFEITORIA_TIPO_ACESS ng-model=benfeitoria.tipoBenfeitoria ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoAcessaoBenfeitoria ng-disabled=isDetalhar()><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposBenfeitoria | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><!-- Cadastro de Geometria --><v-pane ng-show=isBenfeitoriaEdificacao()><v-pane-header>Cadastro de Geometria</v-pane-header><v-pane-content><div layout=column><div layout=column><md-radio-group ng-model=ctrl.tipoDesenhoBenfeitoria ng-change=alterarTipoDesenho() layout=row ng-disabled=isDetalhar() layout-margin><md-radio-button campo-ajuda=INC_FORM_BENFEITORIA_ARQ_SHAPE ng-value=1 flex>Arquivo (Shapefile)</md-radio-button><md-radio-button campo-ajuda=INC_FORM_BENFEITORIA_VETORIZACAO ng-value=2 flex>Vetorização</md-radio-button></md-radio-group></div><ci-input-file style=\"margin-top: 30px\" model=filesBenfeitoria.shapefile desabilitado=\"ctrl.tipoDesenhoBenfeitoria !== 1 || isDetalhar()\" objeto=benfeitoria.arquivoGeometria botao=exibirBotao cif-onchange=uploadShapefileBenfeitoria() cif-onremove=limparUploadShapefileBenfeitoria()></ci-input-file></div></v-pane-content></v-pane><!-- Dados da Benfeitoria --><v-pane ng-show=\"isBenfeitoriaComplementar() || isBenfeitoriaEdificacao()\"><v-pane-header>Dados da Acessão/Benfeitoria</v-pane-header><v-pane-content><div layout=column><div layout=row><md-input-container ng-show=isBenfeitoriaEdificacao() flex><label id=tipoEspecializacaoEdificacaoLabel>Tipo de Especialização de Edificação</label><!--<md-select\n                    campo-ajuda=\"INC_FORM_BENFEITORIA_TIP_ESP_EDIFIC\"\n                    ng-model=\"benfeitoria.edificacao.tipoEspecializacaoEdificacao\"\n                    ng-model-options=\"{trackBy: \'$value.id\'}\"\n                    ng-required=\"isBenfeitoriaEdificacao()\"\n                    name=\"tipoEspecializacaoEdificacao\"\n                    ng-disabled=\"isDetalhar()\"\n                  >\n                    <md-option\n                      campo-ajuda=\"{{item.codigoSistemico}}\"\n                      ng-repeat=\"item in tiposEspecializacaoEdificacao\"\n                      ng-value=\"item\"\n                    >\n                      {{item.descricao}}\n                    </md-option>\n                  </md-select>--><md-autocomplete campo-ajuda=INC_FORM_BENFEITORIA_TIP_ESP_EDIFIC name=tipoEspecializacaoEdificacao ng-required=isBenfeitoriaEdificacao() ng-disabled=isDetalhar() md-clear-button=!$mdAutocompleteCtrl.isDisabled md-selected-item=benfeitoria.edificacao.tipoEspecializacaoEdificacao md-search-text=$scope.searchTipoEspecializacaoEdificacao md-items=\"item in tiposEspecializacaoEdificacao | filter:{descricao:$scope.searchTipoEspecializacaoEdificacao} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=tipoEspecializacaoEdificacaoLabel><md-item-template><span md-highlight-text=$scope.searchTipoEspecializacaoEdificacao md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchTipoEspecializacaoEdificacao}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container ng-show=isBenfeitoriaEdificacao() flex><label id=tipoProjetoLabel>Tipo de Projeto</label><md-select campo-ajuda=INC_FORM_BENFEITORIA_TIPO_PROJ ng-model=benfeitoria.edificacao.tipoProjeto ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=isBenfeitoriaEdificacao() name=tipoProjeto ng-disabled=isDetalhar()><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposProjeto | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select><!--<md-autocomplete\n                    campo-ajuda=\"INC_FORM_BENFEITORIA_TIPO_PROJ\"\n                    ng-required=\"isBenfeitoriaEdificacao()\"\n                    name=\"tipoProjeto\"\n                    ng-disabled=\"isDetalhar()\"\n                    md-clear-button=\"!$mdAutocompleteCtrl.isDisabled\"\n                    md-selected-item=\"benfeitoria.edificacao.tipoProjeto\"\n                    md-search-text=\"$scope.searchTipoProjeto\"\n                    md-items=\"item in tiposProjeto | filter:{descricao:$scope.searchTipoProjeto} | orderByBr:\'descricao\'\"\n                    md-item-text=\"item.descricao\"\n                    md-min-length=\"0\"\n                    md-escape-options=\"clear\"\n                    md-select-on-match\n                    md-match-case-insensitive\n                    placeholder=\"Informe o dado\"\n                    input-aria-labelledby=\"tipoProjetoLabel\"\n                  >\n                    <md-item-template>\n                      <span\n                        md-highlight-text=\"$scope.searchTipoProjeto\"\n                        md-highlight-flags=\"^i\"\n                        >{{item.descricao}}</span\n                      >\n                    </md-item-template>\n                    <md-not-found>\n                      Nenhum resultado para \"{{$scope.searchTipoProjeto}}\" foi\n                      encontrado.\n                    </md-not-found>\n                  </md-autocomplete>--></md-input-container><md-input-container ng-show=isBenfeitoriaComplementar() flex><label id=tipoEspecializacaoComplementarLabel>Tipo de Especialização Complementar</label><!--<md-select\n                    campo-ajuda=\"INC_FORM_BENFEITORIA_TIPO_ESPEC_COMP\"\n                    ng-model=\"benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar\"\n                    ng-model-options=\"{trackBy: \'$value.id\'}\"\n                    ng-required=\"isBenfeitoriaComplementar()\"\n                    name=\"tipoEspecializacaoComplementar\"\n                    ng-disabled=\"isDetalhar()\"\n                  >\n                    <md-option\n                      campo-ajuda=\"{{item.codigoSistemico}}\"\n                      ng-repeat=\"item in tiposEspecializacaoComplementar\"\n                      ng-value=\"item\"\n                    >\n                      {{item.descricao}}\n                    </md-option>\n                  </md-select>--><md-autocomplete campo-ajuda=INC_FORM_BENFEITORIA_TIPO_ESPEC_COMP ng-required=isBenfeitoriaComplementar() name=tipoEspecializacaoComplementar ng-disabled=isDetalhar() md-clear-button=!$mdAutocompleteCtrl.isDisabled md-selected-item=benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar md-search-text=$scope.searchTipoEspecializacaoComplementar md-items=\"item in tiposEspecializacaoComplementar | filter:{descricao:$scope.searchTipoEspecializacaoComplementar} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=tipoEspecializacaoComplementarLabel><md-item-template><span md-highlight-text=$scope.searchTipoEspecializacaoComplementar md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchTipoEspecializacaoComplementar}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container></div><div layout=row ng-show=isBenfeitoriaEdificacao()><md-input-container class=md-block flex><label>Descrição</label><input campo-ajuda=INC_FORM_BENFEITORIA_NOME ng-required=isBenfeitoriaEdificacao() name=nome type=text ng-model=benfeitoria.nome ng-disabled=isDetalhar()></md-input-container></div><div layout=row ng-show=benfeitoria.tipoBenfeitoria.id><md-input-container flex><label>Área Construída(m²)</label><input campo-ajuda=INC_FORM_BENFEITORIA_AREA_CONST_COMPL type=text ng-model=benfeitoria.areaConstruida ui-number-mask=2 ng-required=true name=areaConstruida ng-disabled=isDetalhar()></md-input-container><md-input-container flex ng-show=isBenfeitoriaEdificacao()><label>Área de Projeção(m²)</label><input campo-ajuda=INC_FORM_BENFEITORIA_AREA_PROJ type=text ng-model=benfeitoria.edificacao.areaProjecao ui-number-mask ng-required=isBenfeitoriaEdificacao() ng-blur=validarAreaProjecao() name=areaProjecao ng-disabled=isDetalhar()></md-input-container></div><div layout=row ng-show=isBenfeitoriaEdificacao()><!--alterado de taxa de ocupacao para Fração de Ocupação--><md-input-container class=md-input-has-placeholder flex ng-disabled=isDetalhar()><label>Fração de Ocupação</label><div campo-ajuda=INC_FORM_BENFEITORIA_TAXA_OCUP class=md-input ng-model=benfeitoria.edificacao.taxaOcupacao disabled>{{getTaxaOcupacao() | number:2}}</div></md-input-container><md-input-container class=md-input-has-placeholder flex ng-disabled=isDetalhar() ng-model=benfeitoria.edificacao.indiceAproveitamento><label>Índice de Aproveitamento</label><div campo-ajuda=INC_FORM_BENFEITORIA_INDIC_APROVEIT class=md-input disabled>{{getIndiceAproveitamento() | number:2}}</div></md-input-container></div><div layout=row ng-show=isBenfeitoriaEdificacao()><md-input-container flex><label>Número de Pavimentos</label><input campo-ajuda=INC_FORM_BENFEITORIA_N_PAVIM ng-model=benfeitoria.edificacao.numeroPavimentos type=number ng-required=isNumeroPavimentosRequired() name=numeroPavimentos ng-disabled=\"isNumeroPavimentosDisabled() || isDetalhar()\"></md-input-container><md-input-container ng-show=\"benfeitoria.edificacao.tipoProjeto.descricao === \'Residencial\'\" flex><label>Número de Dormitórios</label><input campo-ajuda=INC_FORM_BENFEITORIA_N_DORM type=number ng-model=benfeitoria.edificacao.numeroDormitorios name=numeroDormitorios ng-disabled=isDetalhar()></md-input-container></div><div layout=row ng-show=\"isBenfeitoriaEdificacao() && imovelPossuiMatricula\"><md-input-container><md-checkbox campo-ajuda=INC_FORM_BENFEITORIA_AVERB_MATR_IMOV ng-model=benfeitoria.edificacao.averbada ng-disabled=isDetalhar()>Averbada na matrícula do imóvel</md-checkbox></md-input-container></div><md-input-container ng-show=\"isBenfeitoriaEdificacao() && imovelPossuiMatricula && benfeitoria.edificacao.averbada\"><label>Descrição da Averbação</label><input campo-ajuda=INC_FORM_BENFEITORIA_DESCR_AVERB type=text ng-model=benfeitoria.edificacao.descricaoAverbacao ng-required=\"isBenfeitoriaEdificacao() && imovelPossuiMatricula && benfeitoria.edificacao.averbada\" name=averbada ng-disabled=isDetalhar()></md-input-container><div layout=column ng-show=isBenfeitoriaEdificacao()><div campo-ajuda=INC_FORM_BENFEITORIA_POSSUI_GARAG ng-class=\"!benfeitoria.edificacao.possuiGaragem && submittedBenfeitoria ? \'input-invalid\' : null\" layout-margin>Possui garagem? *</div><md-radio-group ng-class=\"!benfeitoria.edificacao.possuiGaragem && submittedBenfeitoria ? \'radio-invalid\' : null\" ng-model=benfeitoria.edificacao.possuiGaragem ng-disabled=isDetalhar()><div layout=row style=\"margin: 0 20px\"><md-radio-button campo-ajuda=INC_FORM_BENFEITORIA_POSSUI_GARAG_SIM value=Sim flex>Sim</md-radio-button><md-radio-button campo-ajuda=INC_FORM_BENFEITORIA_POSSUI_GARAG_NAO value=Não flex>Não</md-radio-button><md-radio-button campo-ajuda=INC_FORM_BENFEITORIA_POSSUI_GARAG_SEM_INF value=\"Sem Informação\" flex>Sem Informação</md-radio-button></div></md-radio-group></div><div layout=column ng-show=\"isBenfeitoriaEdificacao() && isBenfeitoriaComGaragem()\"><div campo-ajuda=INC_FORM_BENFEITORIA_N_VAGAS layout-margin>Número de Vagas</div><div layout=row><md-input-container flex><label>Cobertas</label><input campo-ajuda=INC_FORM_BENFEITORIA_COBERTAS type=number ng-model=benfeitoria.edificacao.numeroVagasCobertas ng-required=\"isBenfeitoriaEdificacao() && isBenfeitoriaComGaragem()\" name=numeroVagasCobertas ng-disabled=isDetalhar()></md-input-container><md-input-container flex><label>Descobertas</label><input campo-ajuda=INC_FORM_BENFEITORIA_DESCOBERTAS type=number ng-model=benfeitoria.edificacao.numeroVagasDescobertas ng-required=\"isBenfeitoriaEdificacao() && isBenfeitoriaComGaragem()\" name=numeroVagasDescobertas ng-disabled=isDetalhar()></md-input-container><div flex layout=row layout-align=\"center center\">Total = {{benfeitoria.edificacao.numeroVagasCobertas + benfeitoria.edificacao.numeroVagasDescobertas || 0}}</div></div></div><div layout=row ng-show=\"isBenfeitoriaEdificacao() && benfeitoria.edificacao.numeroPavimentos > 1\"><md-input-container><md-checkbox campo-ajuda=INC_FORM_BENFEITORIA_POSSUI_ELEVAD ng-model=benfeitoria.edificacao.possuiElevador ng-disabled=isDetalhar()>Possui Elevador</md-checkbox></md-input-container></div><div layout=row ng-show=isBenfeitoriaEdificacao()><md-input-container><md-checkbox campo-ajuda=INC_FORM_BENFEITORIA_POSSUI_ENCE ng-model=benfeitoria.edificacao.possuiENCE ng-disabled=isDetalhar()>Possui Etiqueta Nacional de Conservação de Energia (ENCE)</md-checkbox></md-input-container></div><div layout=row ng-show=\"isBenfeitoriaEdificacao() && benfeitoria.edificacao.possuiENCE\"><md-input-container flex><label>Tipo</label><md-select campo-ajuda=INC_FORM_BENFEITORIA_TIPO_ENCE ng-model=benfeitoria.edificacao.tipoEnce ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"isBenfeitoriaEdificacao() && benfeitoria.edificacao.possuiENCE\" name=tipoEnce ng-disabled=isDetalhar()><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposEnce | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-show=\"benfeitoria.edificacao.tipoEnce.id === 2\" flex><label>Tipo de Parcial</label><md-select campo-ajuda=INC_FORM_BENFEITORIA_TIPO_ENCE_PARCIAL ng-model=benfeitoria.edificacao.tipoEnceParcial ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"isBenfeitoriaEdificacao() && benfeitoria.edificacao.possuiENCE && benfeitoria.edificacao.tipoEnce.id === 2\" name=tipoEnceParcial ng-disabled=isDetalhar()><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposEnceParcial | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex><label>Nível</label><md-select campo-ajuda=INC_FORM_BENFEITORIA_NIVEL ng-model=benfeitoria.edificacao.nivelEnce ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"isBenfeitoriaEdificacao() && benfeitoria.edificacao.possuiENCE\" name=nivelEnce ng-disabled=isDetalhar()><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in niveisEnce | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><md-input-container><label>Descrição/Observações da Benfeitoria</label><textarea campo-ajuda=INC_FORM_BENFEITORIA_DESCR_COMPL ng-model=benfeitoria.observacoes md-maxlength=1000 ng-disabled=isDetalhar()></textarea></md-input-container><md-input-container ng-show=isBenfeitoriaEdificacao()><label>Observações Gerais da Edificação</label><textarea campo-ajuda=INC_FORM_BENFEITORIA_OBS_GERAIS ng-model=benfeitoria.edificacao.observacoes md-maxlength=2000 ng-disabled=isDetalhar()></textarea></md-input-container></div><!-- Dados do medidor --><dados-medidor action={{action}} ng-show=isBenfeitoriaEdificacao() uf=imovel.enderecamento.uf model=benfeitoria.edificacao.dadosMedidor></dados-medidor></v-pane-content></v-pane><!-- Condições de Acessibilidade --><v-pane ng-show=isBenfeitoriaEdificacao()><v-pane-header>Condições de Acessibilidade</v-pane-header><v-pane-content><div layout=column><md-input-container><md-checkbox campo-ajuda=INC_FORM_BENFEITORIA_SEM_INFORM_ACESS ng-model=benfeitoria.edificacao.semInformacaoAcessibilidade ng-disabled=isDetalhar() ng-change=updateInformacoesAcessibilidade();>Sem informações de acessibilidade</md-checkbox></md-input-container><div layout=row><md-input-container flex class=margem-direita-padrao><div>Possui Laudo de Acessibilidade?</div><md-select campo-ajuda=INC_FORM_BENFEITORIA_LAUDO ng-model=benfeitoria.edificacao.possuiLaudo placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=atualizarLinkLaudo(); ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=possuiLaudo><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex><div>Link do Laudo de Acessibilidade</div><input ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar() || benfeitoria.edificacao.possuiLaudo.id !== 2\" type=text ng-model=benfeitoria.edificacao.linkLaudo name=linkLaudo maxlength=150 md-maxlength=150 aria-label=\" Link do Laudo de Acessibilidade\"></md-input-container></div><div layout=row style=\"margin-top: -30px\"><md-input-container flex class=margem-direita-padrao><div>Possui Plano de Trabalho?</div><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=benfeitoria.edificacao.possuiPlanoTrabalho placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=atualizarLinkPlano(); ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=possuiPlanoTrabalho><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex><div>Link do Plano de Trabalho</div><input ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar() || benfeitoria.edificacao.possuiPlanoTrabalho.id !== 2\" type=text ng-model=benfeitoria.edificacao.linkPlanoTrabalho name=linkPlano md-maxlength=150 aria-label=\" Link do plano de trabalho\"></md-input-container></div><div style=\"margin-top: -20px\">Vagas para Pessoa com Deficiência reservadas e sinalizadas perto do acesso (2% do total de vagas)</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=benfeitoria.edificacao.vagasDefFisicos placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=vagasDefFisicos><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Vagas para Pessoa Idosa reservadas e sinalizadas perto do acesso (5% do total de vagas).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_IDOSOS ng-model=benfeitoria.edificacao.vagasIdosos placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=vagasIdosos><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Rebaixamento de calçadas.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_REBAIXA_CALCADAS ng-model=benfeitoria.edificacao.calcadasRebaixa placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=calcadasRebaixa><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Calçadas sem barreiras nas proximidades da edificação.</div><md-input-container><md-select campo-ajuda=\"INC_FORM_BENFEITORIA_CALCADAS\'\" ng-model=benfeitoria.edificacao.calcadasLivres placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" ng-required=\"benfeitoria.edificacao.possuiElevador && !benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" name=calcadasLivres><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Entrada livre de barreiras (catracas, porta giratória, trilhos não embutidos, degraus, entre outros).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_ENTRADA_LIVRE ng-model=benfeitoria.edificacao.entradaLivre placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=entradaLivre><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Todos os pavimentos podem ser acessados por pessoa em cadeira de rodas.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_CADEIRA_RODAS ng-model=benfeitoria.edificacao.pavimentosLivreCadRodas placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=pavimentosLivreCadRodas><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Balcão de informação acessível (com pelo menos um trecho rebaixado conforme norma técnica).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_INFORMACOES ng-model=benfeitoria.edificacao.balcaoInformacoes placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=balcaoInformacoes><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Piso Tátil.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_PISO_TATIL ng-model=benfeitoria.edificacao.pisoTatil placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=pisoTatil><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Corredores com largura adequada e sem obstáculos.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_CORREDOR_LARGO ng-model=benfeitoria.edificacao.corredorLargo placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=corredorLargo><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Sanitário acessível de uso público.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_SANITARIO_PUBLICO ng-model=benfeitoria.edificacao.sanitarioPublico placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=sanitarioPublico><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Sinalização em braile e alto relevo junto a informações relevantes (ex. sanitários, elevadores).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_BRAILE ng-model=benfeitoria.edificacao.braile placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=braile><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div>Atendimento em LIBRAS.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_LIBRAS ng-model=benfeitoria.edificacao.libras placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"!benfeitoria.edificacao.semInformacaoAcessibilidade && benfeitoria.tipoBenfeitoria.id === 1\" ng-disabled=\"benfeitoria.edificacao.semInformacaoAcessibilidade || isDetalhar()\" name=libras><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><!-- </div> --></div></v-pane-content></v-pane><!-- Documentação --><v-pane ng-show=isBenfeitoriaEdificacao()><v-pane-header>Documentação</v-pane-header><v-pane-content><dados-documentacao action=benfeitoria model=benfeitoria.edificacao.documentacao show-tab-form-documento=showTabFormDocumento show-tab-inclusao-benfeitoria=showInclusaoBenfeitoria documento=documento documento-backup=documentoBackup documentos-inc=documentos detail=\"action==\'detail\'\"></dados-documentacao></v-pane-content></v-pane><!-- Fotos --><v-pane><v-pane-header>Fotos</v-pane-header><v-pane-content><fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">Fotos</label></legend><div layout=column><ng-form isolate-form ng-show=!isDetalhar() name=forms.imagemBenfeitoriaForm><div layout=row><md-input-container flex><label>Descrição</label><input campo-ajuda=INC_FORM_UPLOAD_FOTOS_DESC required ng-model=fotoBenfeitoria.descricao type=text name=descricao maxlength=50></md-input-container><md-input-container flex><label>Data</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_UPLOAD_FOTOS_DT_DOC required ng-model=fotoBenfeitoria.dataEmissao md-max-date=hoje name=dataEmissao></md-datepicker></md-input-container><md-input-container><input required type=file ngf-select ng-model=ctrl.fotoFile accept=\".jpeg, .png, .jpg, .bmp\" name=foto aria-label=Arquivo flex></md-input-container></div><div layout=row layout-align=\"end center\"><md-button class=\"md-raised md-primary primeira-maiuscula\" ng-click=adicionarFotoBenfeitoria()>Adicionar</md-button></div></ng-form><div layout=column ng-show=\"benfeitoria.imagens.length > 0 || isDetalhar()\"><md-table-container><table md-table md-progress=promise><thead md-head md-order=ordenacaoFotos><tr md-row><th md-column></th><th md-column md-order-by=descricao>Descrição</th><th md-column md-order-by=dataEmissao>Data</th><th md-column md-order-by=nomeOriginal>Arquivo</th><th md-column ng-show=!isDetalhar()>Ações</th></tr></thead><tbody md-body><tr md-row ng-show=\"benfeitoria.imagens.length === 0\"><td md-cell>Nenhum Registro</td></tr><tr md-row ng-repeat=\"imagem in benfeitoria.imagens | orderBy: ordenacaoFotos | limitTo: paginacaoFotos.limit: (paginacaoFotos.page - 1) * paginacaoFotos.limit\"><td md-cell><img ng-src=data:image/jpg;base64,{{imagem.imagem}} style=\"width: 70px; height: 40px\"></td><td md-cell>{{imagem.descricao}}</td><td md-cell>{{imagem.dataEmissao | date:\"dd/MM/yyyy\"}}</td><td md-cell><a ng-href=\"{{ imagem.url }}\" download=\"{{ imagem.nomeOriginal }}\">{{ imagem.nomeOriginal }}</a></td><td md-cell ng-show=!isDetalhar()><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"excluirFotoBenfeitoria($event, imagem)\" class=\"md-fab md-raised md-mini\" aria-label=\"Excluir Arquivo\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir Arquivo</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacaoFotos.limit md-limit-options=paginacaoFotos.limitOptions md-page=paginacaoFotos.page md-total=\"{{ benfeitoria.imagens.length }}\" md-page-select ng-disabled=\"benfeitoria.imagens.length < 6\"></md-table-pagination></div></div></fieldset></v-pane-content></v-pane><!-- Arquivos de Planta --><v-pane><v-pane-header>Arquivos de Planta</v-pane-header><v-pane-content><fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">Arquivos de Planta</label></legend><div layout=column><ng-form isolate-form name=forms.plantaBenfeitoriaForm><div layout=row><md-input-container ng-show=!isDetalhar() flex><label>Descrição</label><input campo-ajuda=INC_FORM_UPLOAD_ARQUIVOS_DESC required ng-model=plantaBenfeitoria.nome type=text name=nome maxlength=200></md-input-container><md-input-container ng-show=!isDetalhar() flex><label>Data</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_UPLOAD_ARQUIVOS_DT_DOC required class=special ng-model=plantaBenfeitoria.data md-max-date=hoje name=data></md-datepicker></md-input-container><md-input-container ng-show=!isDetalhar()><input required type=file ngf-select ng-model=ctrl.plantaFile name=planta aria-label=Arquivo flex></md-input-container></div><div layout=row layout-align=\"end center\"><md-button ng-show=!isDetalhar() ng-click=adicionarPlantaBenfeitoria() md-no-ink class=\"md-raised md-primary primeira-maiuscula\">Adicionar</md-button></div></ng-form><div layout=column ng-show=\"benfeitoria.plantas.length > 0 || isDetalhar()\"><md-table-container><table md-table md-progress=promise><thead md-head md-order=ordenacaoPlantas><tr md-row><th md-column md-order-by=nome>Descrição</th><th md-column md-order-by=data>Data</th><th md-column md-order-by=nomeOriginal>Arquivo(Download)</th><th md-column ng-show=!isDetalhar()>Ações</th></tr></thead><tbody md-body><tr md-row ng-show=\"benfeitoria.plantas.length === 0\"><td md-cell>Nenhum Registro</td></tr><tr md-row ng-repeat=\"planta in benfeitoria.plantas | orderBy: ordenacaoPlantas | limitTo: paginacaoPlantas.limit: (paginacaoPlantas.page - 1) * paginacaoPlantas.limit\"><td md-cell>{{planta.nome}}</td><td md-cell>{{planta.data | date:\"dd/MM/yyyy\"}}</td><td md-cell><a download={{planta.nomeOriginal}} href={{planta.url}} target=_blank>{{planta.nomeOriginal}}</a></td><td md-cell ng-show=!isDetalhar()><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"excluirPlantaBenfeitoria($event, planta)\" class=\"md-fab md-raised md-mini\" aria-label=\"Excluir Arquivo\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir Arquivo</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacaoPlantas.limit md-limit-options=paginacaoPlantas.limitOptions md-page=paginacaoPlantas.page md-total=\"{{ benfeitoria.plantas.length }}\" md-page-select ng-disabled=\"benfeitoria.plantas.length < 6\"></md-table-pagination></div></div></fieldset></v-pane-content></v-pane></v-accordion></ng-form></div></div><div layout=row layout-align=\"end center\"><md-button class=\"md-secondary md-raised\" ng-click=cancelarInclusaoBenfeitoria();>Fechar</md-button><md-button class=\"md-raised md-secondary\" ng-click=limparInclusaoBenfeitoria(); ng-if=\"!isDetalhar() && benfeitoria.id==null\">Limpar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=confirmarInclusaoBenfeitoria($event) ng-if=!isDetalhar()>Salvar</md-button></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/condicoes-acessibilidade/templates/condicoes-acessibilidade.html","<div layout=column><div layout=column><md-input-container><md-checkbox campo-ajuda=INC_FORM_UN_AUTONOMA_SEM_INFORM_ACESS ng-disabled=\"condicoesAcessibilidadeDisabled() && !clicou\" ng-model=model.semInformacoesAcessibilidade name=semInformacoesAcessibilidade ng-change=updateInformacoesAcessibilidade();>Sem informações de acessibilidade</md-checkbox></md-input-container><div layout=row><md-input-container flex class=margem-direita-padrao><div>Possui Laudo de Acessibilidade?</div><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.possuiLaudo placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=atualizarLinkLaudo(); ng-disabled=condicoesAcessibilidadeDisabled() name=possuiLaudo ng-required=\"model.semInformacoesAcessibilidade!=true\"><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex><div>Link do Laudo de Acessibilidade</div><input type=text ng-model=model.linkLaudo name=linkLaudo maxlength=150 md-maxlength=150 ng-disabled=condicoesAcessibilidadeDisabled() aria-label=\" Link do laudo\"></md-input-container></div><div layout=row style=\"margin-top: -30px\"><md-input-container flex class=margem-direita-padrao><div>Possui Plano de Trabalho?</div><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.possuiPlanoTrabalho placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=atualizarLinkPlano(); ng-disabled=condicoesAcessibilidadeDisabled() name=possuiPlanoTrabalho ng-required=\"model.semInformacoesAcessibilidade!=true\"><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex><div>Link do Plano de Trabalho</div><input type=text ng-model=model.linkPlanoTrabalho name=linkPlano md-maxlength=150 ng-disabled=condicoesAcessibilidadeDisabled() aria-label=\" Link do plano de trabalho\"></md-input-container></div><div layout=column><div>Vagas para Pessoa com Deficiência reservadas e sinalizadas perto do acesso (2% do total de vagas).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.vagasDefFisicos placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=condicoesAcessibilidadeDisabled() name=vagasDefFisicos ng-required=\"model.semInformacoesAcessibilidade!=true\"><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><br><div layout=column><div>Vagas para Pessoa Idosa reservadas e sinalizadas perto do acesso (5% do total de vagas).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.vagasIdosos placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=vagasIdosos><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><div>Rebaixamento de calçadas.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.calcadasRebaixa placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=calcadasRebaixa><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><!--ng-hide=\"isGaragem\" --><div>Calçadas sem barreiras nas proximidades da edificação</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.calcadasLivres placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=calcadasLivres><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><!-- ng-show=\"!isGaragem && possuiElevador\" --><div>Entrada livre de barreiras (catracas, porta giratória, trilhos não embutidos, degraus, entre outros).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.entradaLivre placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=entradaLivre><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><!-- ng-show=\"!isGaragem && possuiElevador\" --><div>Todos os pavimentos podem ser acessados por pessoa em cadeira de rodas.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.pavimentosLivreCadRodas placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=pavimentosLivreCadRodas><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><!-- ng-show=\"!isGaragem && possuiElevador\" --><div>Balcão de informação acessível (com pelo menos um trecho rebaixado conforme norma técnica).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.balcaoInformacoes placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=balcaoInformacoes><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><!-- ng-show=\"!isGaragem && possuiElevador\" --><div>Piso Tátil.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.pisoTatil placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=pisoTatil><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><!-- ng-show=\"!isGaragem && possuiElevador\" --><div>Corredores com largura adequada e sem obstáculos.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.corredorLargo placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=corredorLargo><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><div>Sanitário acessível de uso público.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.sanitarioPublico placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=sanitarioPublico><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><!-- ng-show=\"!isGaragem && possuiElevador\" --><div>Sinalização em braile e alto relevo junto a informações relevantes (ex. sanitários, elevadores).</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.braile placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=braile><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><!-- ng-show=\"!isGaragem && possuiElevador\" --><div>Atendimento em LIBRAS.</div><md-input-container><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.libras placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"model.semInformacoesAcessibilidade!=true\" ng-disabled=condicoesAcessibilidadeDisabled() name=libras><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/dados-documentacao/templates/dados-documentacao.html","<div layout=column><div layout=column><fieldset class=fieldset><legend style=\"color: rgb(76, 175, 80)\">Documentação</legend><div layout=column ng-show=\"model.length > 0\"><md-table-container><table md-table><thead md-head md-order=ordenacao><tr md-row><th md-column md-order-by=nome>Documento</th><th md-column md-order-by=possui>Possui</th><th md-column md-order-by=dataEmissao>Emissão</th><th md-column md-order-by=dataValidade>Validade</th><th md-column md-order-by=id>Arquivo</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in model | orderBy: ordenacao | limitTo: paginacao.limit: (paginacao.page - 1) * paginacao.limit\"><td md-cell>{{documento.nome}}</td><td md-cell>{{documento.possui == \'SIM\'? \'Sim\' : documento.possui == \'NAO\'? \'Não\' : \'Sem informação\'}}</td><td md-cell ng-class=\"documento.dataEmissao && documento.possui==\'SIM\' ? \'\' : \'md-cell-null\'\">{{documento.possui == \'SIM\'? (documento.dataEmissao | date : \"dd/MM/yyyy\") || \"-\" : \"-\" }}</td><td md-cell ng-class=\"documento.dataValidade && documento.possui==\'SIM\' ? \'\' : \'md-cell-null\'\">{{documento.possui == \'SIM\'? (documento.dataValidade | date : \"dd/MM/yyyy\") || \"-\" : \"-\" }}</td><td md-cell><a download={{documento.arquivo.nomeOriginal}} href={{documento.url}} target=_blank ng-if=\"documento.possui==\'SIM\'\">{{\'Visualizar\'}}</a><p ng-if=\"documento.possui==\'NAO\' || documento.possui==\'SEM_INFORMACAO\'\">-</p></td><td md-cell><div class=container-acoes ng-if=!dadosDocumentacaoDisabled()><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"excluirDocumento($event, documento)\" class=\"md-fab md-raised md-mini\" aria-label=\"Excluir Arquivo\" ng-if=\"!documento.isPrecadastrado && !dadosDocumentacaoDisabled()\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir Arquivo</md-tooltip></md-icon></md-button><md-button ng-click=exibeTab(documento) class=\"md-fab md-raised md-mini\" aria-label=\"Editar Documento\" ng-show=!dadosDocumentacaoDisabled()><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Documento</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><div layout=column style=\"padding-top: 10px\" flex><div layout=row layout-align=\"end center\"><md-button ng-show=!dadosDocumentacaoDisabled() class=md-primary ng-click=exibeTab()>Incluir novo documento</md-button></div></div><md-table-pagination md-limit=paginacao.limit md-limit-options=paginacao.limitOptions md-page=paginacao.page md-total=\"{{ model.length }}\" md-page-select ng-disabled=\"model.length < 6\"></md-table-pagination></div></fieldset></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/dados-documentacao/templates/form-documento.html","<div layout=column><ng-form name=forms.inclusaoDocumentoForm><div layout=row><md-input-container flex=50><label>Nome do Documento</label><input required campo-ajuda=INC_FORM_DOCUMENTACAO_NOME_DOC ng-model=documento.nome ng-disabled=documento.isPrecadastrado></md-input-container><md-radio-group ng-class=\"!documento.possui && submittedFormDocumentos ? \'radio-invalid md-off\' : null\" ng-model=documento.possui><label ng-class=\"!documento.possui && submittedFormDocumentos ? \'input-invalid\' : null\" campo-ajuda=INC_FORM_DOCUMENTACAO_POSSUI_DOC>Possui esse documento? *</label><div layout=row layout-margin><md-radio-button campo-ajuda=INC_FORM_DOCUMENTACAO_POSSUI_DOC_SIM ng-value=\"\'SIM\'\">Sim</md-radio-button><md-radio-button campo-ajuda=INC_FORM_DOCUMENTACAO_POSSUI_DOC_NAO ng-value=\"\'NAO\'\">Não</md-radio-button></div></md-radio-group></div><div layout=row ng-if=isPossuiDocumento()><md-input-container flex=50><ci-input-file show-input=submittedFormDocumentos model=documento.arquivo objeto=documento.arquivo accept=\".pdf,.doc,.docx,.ods,.txt, .jpeg, .png, .jpg, .bmp\" cif-onchange=uploadArquivo() obrigatorio=isPossuiDocumento() botao=true></ci-input-file></md-input-container><md-input-container flex=25><label>Data de Emissão</label><md-datepicker ng-mask=39/19/9999 restrict=reject required campo-ajuda=INC_FORM_DOCUMENTACAO_DT_EMISSAO ng-model=documento.dataEmissao ng-required=isPossuiDocumento() md-max-date=today name=data></md-datepicker></md-input-container><md-input-container flex=25><label>Data de Validade</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_DOCUMENTACAO_DT_VALIDADE ng-model=documento.dataValidade name=data></md-datepicker></md-input-container></div></ng-form></div><div layout=row layout-align=\"end center\"><md-button class=\"md-secondary md-raised\" ng-click=cancelarInclusaoDocumento();>Fechar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=incluirDocumento();>Salvar</md-button></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/templates/classificacao-utilizacao.html","<fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">Classificação</label></legend><div layout=column flex=100><md-input-container><md-checkbox ng-disabled=isDetalhar() ng-model=imovel.reservaImovelFuncional>Reserva de imóvel funcional</md-checkbox></md-input-container><md-input-container><md-checkbox ng-disabled=isDetalhar() ng-model=imovel.perfilVenda>Perfil para venda</md-checkbox></md-input-container><md-input-container><md-checkbox ng-disabled=isDetalhar() ng-model=\"imovel.perfilPoliticaPublica  \">Perfil para política pública</md-checkbox></md-input-container></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/templates/dados-incorporacao.html","<div id=secaoDadosIncorporacao layout=column><div layout=row ng-hide=\"isEditar() || isDetalhar()\"><div style=\"font-size: 12px\">Processo SEI *<md-tooltip class=tooltip>A integração entre SPUnet e SEI-ME tem por objetivo vincular o cadastro do imóvel no SPUnet e um processo SEI novo ou existente,<br>além de possibilitar incluir documentos do cadastro no processo.<br>No caso de processo SEI existente, essa funcionalidade depende:<br>1-Da existência do processo no SEI;<br>2-O processo deve ser público e;<br>3-O processo deve estar aberto na mesma unidade SEI da UG Executora do usuário logado, informada no SPUnet.</md-tooltip></div></div><div style=\"margin-bottom: 20px\" layout=row ng-hide=isDetalhar()><label style=\"margin-top: 13px\" ng-show=isEditar()>Opção Selecionada</label></div><div style=\"margin-bottom: 20px\" layout=row ng-hide=isDetalhar()><md-input-container style=\"height: 32px !important\" class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" flex><md-radio-group name=processoSEI layout=row ng-model=imovel.tipoProcessoSei style=\"margin-left: 10px\" ng-hide=isDetalhar() ng-disabled=desabilitar ng-change=resetNumeroProcesso() ng-required=!isDetalhar()><md-radio-button value=criarProcesso>Criar um novo processo SEI na SPU</md-radio-button><md-radio-button value=processoExistente ng-checked=\"isEditar() || isDetalhar()\">Usar um processo SEI público existente aberto na sua unidade SPU</md-radio-button><md-radio-button value=processoExistenteNaoSPU ng-checked=\"isEditar() || isDetalhar()\">Informar um processo não existente no SEI da SPU (Não ocorrerá integração com SEI)</md-radio-button></md-radio-group></md-input-container></div><div layout=row ng-show=\"(imovel.tipoProcessoSei === \'processoExistente\' || imovel.tipoProcessoSei === \'processoExistenteNaoSPU\' || isEditar() || isDetalhar())\"><md-input-container style=\"height: 40px !important\" class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" flex><label style=\"margin-top: 13px\">Número do Processo</label><input campo-ajuda=INC_FORM_DADOS_GERAIS_N_PROCESSO ng-model=imovel.numeroProcesso name=numeroProcesso ng-required=\"imovel.tipoProcessoSei === \'processoExistente\' ||  imovel.tipoProcessoSei === \'processoExistenteNaoSPU\'\" ng-disabled=\"isDetalhar() || blockNumProcesso || desabilitar\" ng-blur=buscaProcessoSEI() style=\"width: 27%\"><div>&nbsp;&nbsp;&nbsp; <a style=\"margin-top: 13px\" ng-href={{imovel.linkProcessoSei}} target=_blank ng-show=\"imovel.linkProcessoSei !== null && imovel.linkProcessoSei !==  undefined\">Link do Processo no SEI</a> <a text-align=right; style=\"color: red;margin-bottom: 30px\" target=_blank ng-show=exibeMengSei>Não foi possível integrar esse número de processo com o SEI. Isso não impede o cadastro do imóvel.</a></div></md-input-container></div><div layout=row><md-input-container ng-class=\"forms.imovelForm.tipoIncorporacao.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=\"md-block padding-padrao-interface margem-baixa-padrao\" flex><label id=tipoIncorporacaoLabel>Forma de Incorporação</label><md-autocomplete campo-ajuda=INC_FORM_DADOS_GERAIS_FORM_INC class=secaoDadosIncorporacao required ng-disabled=\"isTipoImovelCavidade() || isTipoImovelEspelhoDagua() || isDetalhar() || desabilitar\" md-clear-button=\"!isTipoImovelCavidade() && !isTipoImovelEspelhoDagua() && !isDetalhar() && !desabilitar\" name=tipoIncorporacao md-selected-item-change=handleTipoIncorporacaoContratoPessoal();verificarRestricao(); md-selected-item=imovel.incorporacao.tipoIncorporacao md-search-text=$scope.searchTipoIncorporacao md-items=\"item in tiposFormaIncorporacaoFiltrados() | filter:{descricao:$scope.searchTipoIncorporacao} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=tipoIncorporacaoLabel><md-item-template><span ng-if=exibeOpcoesFracionamentoAndFusaoCasoTipoImovelSejaGlebaTerrenoLote(item) md-highlight-text=$scope.searchTipoIncorporacao md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchTipoIncorporacao}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container ng-class=\"forms.imovelForm.tipoAquisicao.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=md-block flex ng-if=isFormaIncorporacaoAquisicao()><label id=tipoAquisicaoLabel>Tipo de Aquisição</label><!--<md-select\n      campo-ajuda=\"INC_FORM_DADOS_GERAIS_TIPO_AQUISICAO\"\n      ng-model=\"imovel.incorporacao.tipoAquisicao\"\n      ng-model-options=\"{trackBy: \'$value.id\'}\"\n      ng-required=\"isFormaIncorporacaoAquisicao()\"\n      name=\"tipoAquisicao\"\n      ng-disabled=\"isDetalhar()\"\n    >\n      <md-option\n        campo-ajuda=\"{{item.codigoSistemico}}\"\n        ng-repeat=\"item in tiposAquisicao\"\n        ng-value=\"item\"\n      >\n        {{item.descricao}}\n      </md-option>\n    </md-select>\n    --><md-autocomplete campo-ajuda=INC_FORM_DADOS_GERAIS_TIPO_AQUISICAO name=tipoAquisicao required ng-disabled=\"isDetalhar() || desabilitar\" md-clear-button=\"!isDetalhar() && !desabilitar\" md-selected-item=imovel.incorporacao.tipoAquisicao md-search-text=$scope.searchTipoAquisicao md-items=\"item in tiposAquisicao| filter:{descricao:$scope.searchTipoAquisicao} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=tipoAquisicaoLabel><md-item-template><span md-highlight-text=$scope.searchTipoAquisicao md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchTipoAquisicao}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container ng-class=\"forms.imovelForm.tipoContrato.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" flex ng-if=isFormaIncorporacaoImoveisDeTerceiros()><label id=tipoContratoLabel>Contrato Pessoal</label><md-autocomplete campo-ajuda=INC_FORM_DADOS_GERAIS_TIPO_CONTR_PESSOAL name=tipoContrato required md-selected-item-change=handleTipoIncorporacaoContratoPessoal() ng-disabled=\"isDetalhar() || desabilitar\" md-clear-button=\"!isDetalhar() && !desabilitar\" md-selected-item=imovel.incorporacao.tipoContratoPessoal md-search-text=$scope.searchTipoContrato md-items=\"item in tiposContratoPessoal| filter:{descricao:$scope.searchTipoContrato} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=tipoContratoLabel><md-item-template><span md-highlight-text=$scope.searchTipoContrato md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchTipoContrato}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container ng-class=\"forms.imovelForm.tipoFracionamento.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=\"md-block margem-baixa-padrao margem-top-padrao\" flex ng-if=isFormaIncorporacaoFracionamento()><label>Tipo de Fracionamento</label><md-select campo-ajuda=INC_FORM_DADOS_GERAIS_TIPO_FRACION ng-model=imovel.tipoFracionamento ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoFracionamento ng-required=isFormaIncorporacaoFracionamento() ng-disabled=\"isDetalhar() || desabilitar\"><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposFracionamento| orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row ng-if=showCessaoOnerosa><md-radio-group ng-class=\"forms.imovelForm.isCessaoOnerosa.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" name=isCessaoOnerosa layout=row ng-disabled=\"isDetalhar() || desabilitar\" ng-model=imovel.incorporacao.isCessaoOnerosa ng-required=\"\" style=\"margin-top: 7px !important\"><md-radio-button ng-value=true style=\"height: 17px !important\">A cessão é onerosa?</md-radio-button><md-input-container ng-class=\"forms.imovelForm.valorCessaoOnerosa.$error.required && submittedImovel?\'md-input-invalid\':null\" ng-show=\"imovel.incorporacao.isCessaoOnerosa == true\" style=\"margin-top: 0\"><label>Valor da Cessão</label><input type=text class=secaoDescricaoCadastral ng-model=imovel.incorporacao.valorCessaoOnerosa ng-disabled=\"isDetalhar() || desabilitar\" ng-required=imovel.incorporacao.isCessaoOnerosa ui-number-mask=2 name=valorCessaoOnerosa></md-input-container><md-radio-button ng-value=false style=\"height: 17px !important\">A cessão é gratuita?</md-radio-button></md-radio-group></div><div layout=row ng-if=showCdruOnerosa><md-radio-group ng-class=\"forms.imovelForm.isCdruOnerosa.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" name=isCessaoOnerosa layout=row ng-disabled=\"isDetalhar() || desabilitar\" ng-model=imovel.incorporacao.isCdruOnerosa ng-required=\"\" style=\"margin-top: 7px !important\"><md-radio-button ng-value=true style=\"height: 17px !important\">Onerosa?</md-radio-button><md-input-container ng-class=\"forms.imovelForm.valorCdruOnerosa.$error.required && submittedImovel?\'md-input-invalid\':null\" ng-show=\"imovel.incorporacao.isCdruOnerosa == true\" style=\"margin-top: 0\"><label>Valor da CDRU</label><input type=text class=secaoDescricaoCadastral ng-model=imovel.incorporacao.valorCdruOnerosa ng-disabled=\"isDetalhar() || desabilitar\" ng-required=imovel.incorporacao.isCdruOnerosa ui-number-mask=2 name=valorCdruOnerosa></md-input-container><md-radio-button ng-value=false style=\"height: 17px !important\">Gratuita?</md-radio-button></md-radio-group></div><div layout=row ng-if=showDireitoSuperficieOneroso><md-radio-group ng-class=\"forms.imovelForm.isDireitoSuperficieOneroso.$error.required && submittedImovel ?\'md-input-invalid\': null\" class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" name=isCessaoOnerosa layout=row ng-disabled=\"isDetalhar() || desabilitar\" ng-model=imovel.incorporacao.isDireitoSuperficieOneroso ng-required=\"\" style=\"margin-top: 7px !important\"><md-radio-button ng-value=true style=\"height: 17px !important\">Oneroso?</md-radio-button><md-input-container ng-class=\"forms.imovelForm.valorDireitoSuperficieOneroso.$error.required && submittedImovel?\'md-input-invalid\':null\" ng-show=\"imovel.incorporacao.isDireitoSuperficieOneroso == true\" style=\"margin-top: 0\"><label>Valor do Direito de Superfície R$:</label><input type=text class=secaoDescricaoCadastral ng-model=imovel.incorporacao.valorDireitoSuperficieOneroso ng-disabled=\"isDetalhar() || desabilitar\" ng-required=imovel.incorporacao.isDireitoSuperficieOneroso ui-number-mask=2 name=valorDireitoSuperficieOneroso></md-input-container><md-radio-button ng-value=false style=\"height: 17px !important\">Gratuito?</md-radio-button></md-radio-group></div><div layout=row ng-if=isTipoAquisicaoRecebimentoEmDoacao()><md-input-container ng-if=isTipoAquisicaoRecebimentoEmDoacao() flex=25 style=\"margin-top: 15px\"><md-checkbox class=INC_FORM_DADOS_GERAIS_DOACAO_ENCARGO ng-model=imovel.incorporacao.doacaoComEncargo ng-disabled=\"isDetalhar() || desabilitar\" ng-click=isDoacaoComEncargo()>Doação com Encargos</md-checkbox></md-input-container></div><md-input-container ng-class=\"forms.imovelForm.outroContatoPessoal.$error.required && submittedImovel? \'md-input-invalid \': null\" class=\"md-block margem-baixa-padrao margem-top-padrao\" flex ng-if=isTipoContratoPessoalOutro()><!--mudado de Outro Contrato Pessoal para Descrição do Contrato/Termo--><label>Descrição do Contrato/Termo</label><input campo-ajuda=INC_FORM_DADOS_GERAIS_OUTRO_CONTR_PESSOAL class=secaoDadosIncorporacao ng-model=imovel.incorporacao.outroContatoPessoal ng-required=isTipoContratoPessoalOutro() ng-disabled=\"isDetalhar() || desabilitar\" name=outroContatoPessoal></md-input-container><!-- Título aquisitivo --><div style=\"margin-top: 20px\" ng-if=\"!(isDetalhar() && !hasTitulo()) && (isFormaIncorporacaoAquisicao() || isFormaIncorporacaoImoveisDeTerceiros())\"><fieldset-titulo-aquisitivo select-index=selectedIndex titulos=imovel.titulos is-exibe-tab-titulo-aquisitivo=isExibeTabTituloAquisitivo titulo=titulo titulo-backup=tituloBackup query-pagination=queryPagination is-detail=\"{{isDetalhar() || desabilitar}}\"></fieldset-titulo-aquisitivo></div><!-- Remembramento de imóveis --><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/fieldsets/unificacao-imoveis-fieldset.html\'\" ng-if=isFormaIncorporacaoFusao()></div><div layout=row><md-input-container ng-class=\"forms.imovelForm.fundamento.$error.required && submittedImovel? \'md-input-invalid\': null\" class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" flex ng-if=isFormaIncorporacaoOriginalmenteUniao() flex ng-controller=SelectFundamentoController ng-cloak><label id=fundamento>Fundamento</label><md-select campo-ajuda=INC_FORM_DADOS_GERAIS_FUNDAMENTO multiple class=secaoDadosIncorporacao ng-model=imovel.incorporacao.fundamento ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=isFormaIncorporacaoOriginalmenteUniao() ng-disabled=\"isDetalhar() || desabilitar\" name=fundamento md-on-close=clearSearchFundamento() data-md-container-class=selectdemoSelectHeader><md-select-header class=demo-select-header><input id=inputFundamento name=inputFundamento ng-model=searchFundamento type=search placeholder=\"Informe o dado\" class=\"demo-header-searchbox md-text\" ng-click=$event.stopPropagation() ng-keydown=$event.stopPropagation()></md-select-header><md-optgroup label=fundamento><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in fundamentos| filter:{descricao:searchFundamento} | orderByBr:\'descricao\'\" ng-value=item ng-if=validaTipoFundamento(item.id)>{{item.descricao}}</md-option></md-optgroup></md-select></md-input-container><md-input-container class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" flex ng-if=showSituacaoIncorporacao()><label>Situação da Incorporação</label><md-select campo-ajuda=INC_FORM_DADOS_GERAIS_SITUACAO_INCOPOR class=secaoDadosIncorporacao ng-model=imovel.incorporacao.situacaoIncorporacao ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"isTipoImovelCavidade() || isTipoImovelEspelhoDagua() || isDetalhar() || desabilitar\" name=situacaoIncorporacao ng-required=true><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in situacoesIncorporacao| orderByBr:\'descricao\'\" ng-value=item ng-if=naoExibeNaoSeAplica(item)>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row style=\"margin-bottom: 10px; margin-left: 0 !important\" layout-margin ng-show=validaExOficio()><md-radio-group class=secaoDescricaoCadastral ng-model=imovel.incorporacao.isExOficio style=\"width: 100%; margin-left: 0 !important\" ng-disabled=\"isDetalhar() || desabilitar\" ng-required=validaExOficio() layout=row ng-change=handleExOficio()><div layout=column flex=50><md-radio-button campo-ajuda=\"\" ng-value=true>Ex-Offício</md-radio-button></div><div layout=column flex=50><md-radio-button campo-ajuda=\"\" ng-value=false>A Pedido</md-radio-button></div></md-radio-group></div><div layout=row ng-if=validaLpm><md-input-container style=\"height: 32px !important\" class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" flex=60><label>Número do Processo LPM/LMEO/Identificação de Área da União</label><input campo-ajuda=INC_FORM_DADOS_GERAIS_N_PROCESSO_LPM ng-model=imovel.incorporacao.numeroProcessoLpm name=numeroProcesso ng-required=validaLpm ng-disabled=\"isDetalhar() || desabilitar\" numero-processo-mask=\"\" maxlength=20></md-input-container><md-input-container flex=40 class=\"margem-top-padrao margem-baixa-padrao\"><label>Data da Homologação</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_DADOS_GERAIS_DT_LPM ng-model=imovel.incorporacao.dataAprovacaoLpm md-placeholder=dd/mm/aaaa ng-disabled=\"isDetalhar() || desabilitar\" class=secaoDadosIncorporacao name=dataAprovacaoLpm md-max-date=hoje></md-datepicker></md-input-container></div><div layout=row ng-if=isFormaIncorporacaoFracionamento()><div flex=50><md-input-container class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\"><label>RIP de Origem</label><input campo-ajuda=INC_FORM_DADOS_GERAIS_RIP_ORIGEM class=secaoDadosIncorporacao ng-model=imovel.incorporacao.codigoOrigem ng-required=isFormaIncorporacaoFracionamento() ng-disabled=\"isDetalhar() || desabilitar\" ng-minlength=8 name=ripOrigem></md-input-container></div><div flex=50 style=\"margin-top: 25px\"><div layout=row><nao-sei-rip style=\"margin-left: 15px\" flex=30 text=\"Não sei o RIP\" multiple-selection=false imovel=imovel></nao-sei-rip><ng-md-icon ng-show=isRipFracionamentoValido icon=search style=\"fill: #788489\"><md-tooltip>Detalhar RIP de Origem</md-tooltip></ng-md-icon><a flex ng-show=isRipFracionamentoValido target=_blank ui-sref=\"INCORPORACAO.detalharImovel({id: idImovelOrigem})\">Detalhar RIP de Origem</a></div></div></div><md-input-container ng-class=\"forms.imovelForm.direitoAdquiridoIncorporado.$error.required && submittedImovel? \'md-input-invalid\': null\" class=\"md-block margem-baixa-padrao margem-top-padrao\" flex ng-if=isFormaIncorporacaoAquisicao()><label>Direito Adquirido/Incorporado</label><md-select campo-ajuda=INC_FORM_DADOS_GERAIS_DIR_ADQUIR_INCORP class=secaoDadosIncorporacao ng-required=true ng-model=imovel.incorporacao.direitoIncorporado ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"isDetalhar() || desabilitar\" name=direitoAdquiridoIncorporado><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in direitosIncorporados\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div class=margem-top-padrao><matricula-transcricao-fieldset ng-if=!isTipoImovelEspelhoDagua() ng-required=\"\" imovel=imovel is-detail=isDetalhar() action={{action}}></matricula-transcricao-fieldset></div><div class=margem-top-padrao><upload-arquivos action={{action}} ng-if=\"hasMatricula() || isDetalhar()\" titulo=\"Documentos Cartoriais\" imovel=imovel model=imovel.incorporacao.certidoesCartoriais></upload-arquivos></div><fieldset-certidoes imovel=imovel ng-if=false></fieldset-certidoes></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/templates/detalhes-imovel.html","<div id=secaoDadosGeraisDoImovel layout=column><div layout=row><md-input-container class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" flex><label>Natureza do Imóvel</label><md-select campo-ajuda=INC_FORM_DADOS_GERAIS_NATUREZA_IMOVEL class=secaoDetalhesImovel ng-disabled=\"isEspelhoDagua() || desabilitar\" ng-model=imovel.naturezaImovel ng-model-options=\"{trackBy: \'$value.id\'}\" required name=naturezaImovel><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposNaturezaImovel\" ng-value=item ng-selected=\"item.descricao === imovel.naturezaImovel.descricao\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\" ng-show=isNaturezaImovelUrbano()><label>N° Inscrição Municipal</label><input campo-ajuda=INC_FORM_DADOS_GERAIS_N_INSCRICAO_MUNICIPAL type=text class=secaoDetalhesImovel ng-disabled=\"isDetalhar() || desabilitar\" ng-model=imovel.numeroInscricaoMunicipal></md-input-container><md-input-container flex class=\"md-block margem-baixa-padrao margem-top-padrao\" ng-show=isNaturezaImovelRural()><label>N° Inscrição Rural</label><input campo-ajuda=INC_FORM_DADOS_GERAIS_N_INSCRICAO_RURAL type=text class=secaoDetalhesImovel ng-disabled=\"isDetalhar() || desabilitar\" ng-model=imovel.numeroInscricaoRural></md-input-container></div><md-input-container class=\"md-block margem-baixa-padrao margem-top-padrao\"><label>Observações Gerais</label><textarea campo-ajuda=INC_FORM_DADOS_GERAIS_OBS_GERAIS class=secaoDetalhesImovel ng-model=imovel.observacoesGerais ng-disabled=\"isDetalhar() || desabilitar\" md-maxlength=500></textarea></md-input-container><div layout=row><md-input-container ng-if=false><md-checkbox ng-disabled=\"isDetalhar() || isImovelSpiunet() || desabilitar\" ng-checked=isImovelLegadoSpiuNet() ng-model=imovelLegado>Imóvel oriundo de um sistema legado do Patrimônio</md-checkbox></md-input-container><md-input-container ng-if=\"imovel && imovel.dsSpiunet && imovel.dsSpiunet !== \'\' && imovel.dsSpiunet !== \'pend. sincronizacao\'\"><md-checkbox ng-disabled=true ng-checked=true ng-model=imovelSpiunet>Imóvel de Uso Especial (para inclusão no SPIUnet)</md-checkbox></md-input-container></div><div layout=row><md-input-container flex ng-if=imovelLegado class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\"><label>Sistema</label><md-select multiple ng-model=imovel.sistema name=sistema ng-disabled=\"imovelSpiunet || desabilitar\" ng-model-options=\"{trackBy: \'$value.id\'}\" required><md-option ng-repeat=\"item in sistema\" ng-disabled=isDetalhar() ng-if=\"isDetalhar() || isAlteracaoImovel() || item.descricao != \'SPIUnet\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row><md-input-container ng-if=\"imovelLegado && isSistemaSIAPA()\" flex class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\"><label>RIP SIAPA</label><input ng-required=\"imovelLegado && isSistemaSIAPA()\" type=text ng-disabled=\"imovelSpiunet || desabilitar\" ng-model=imovel.dsSiapa maxlength=20></md-input-container><md-input-container ng-if=\"imovelLegado && isSistemaCIDISARP()\" flex class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\"><label>NBP</label><input ng-required=\"imovelLegado && isSistemaCIDISARP()\" type=text ng-disabled=\"imovelSpiunet || desabilitar\" ng-model=imovel.dsNbp maxlength=20></md-input-container><!-- Opção desabilitada devido à migração de imóveis SPIUnet para base do SPUnet\n            <md-input-container ng-if=\"isOriundo() && isSistemaSPIUnet()\" flex class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\">\n                <label>RIP SPIUnet</label>\n                <input ng-required=\"isOriundo() && isSistemaSPIUnet()\" type=\"text\" ng-disabled=\"isDetalhar()\" ng-model=\"imovel.dsSpiunet\" maxlength=\"20\">\n            </md-input-container>\n        --><md-input-container ng-if=\"imovelSpiunet && imovel.dsSpiunet && imovel.dsSpiunet !== \'\' && imovel.dsSpiunet !== \'pend. sincronizacao\'\" flex class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\"><label>RIP SPIUnet</label><input ng-required=imovelSpiunet type=text ng-disabled=true ng-model=imovel.dsSpiunet maxlength=20 title=\"O rip será adicionado automaticamente após o imóvel ser salvo\"></md-input-container><md-input-container ng-if=isImovelLegadoSpiuNet() flex class=\"md-block padding-padrao-interface margem-baixa-padrao margem-top-padrao\"><label>RIP SPIUnet Migrado</label><input type=text ng-disabled=true ng-model=ripMigradoSpiuNet maxlength=20></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/templates/dialog-upload-certidao-cartorial.html","<md-dialog aria-label=\"Upload de Certidão Cartorial\" flex=60 ng-cloak><form name=uploadCertidaoCartorial enctype=multipart/form-data><md-toolbar><div class=md-toolbar-tools><h2>Upload de Certidão Cartorial</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><md-input-container class=md-block flex-gt-sm><label>Nome</label><input ng-model=certidaoCartorial.nome type=text required name=nome> <span class=form-error ng-show=\"uploadCertidaoCartorial.nome.$error.required && certidaoCartorialSubmitted\">Campo de preenchimento obrigatório</span></md-input-container><div layout=column><md-datepicker ng-mask=39/19/9999 restrict=reject class=special ng-model=certidaoCartorial.data name=dataDocumento md-placeholder=\"Data do documento\" md-max-date=hoje required name=data></md-datepicker><span class=form-error ng-show=\"uploadCertidaoCartorial.dataDocumento.$error.required && certidaoCartorialSubmitted\">Campo de preenchimento obrigatório</span></div></div><div layout-gt-sm=row layout-margin><input type=file ngf-select ng-model=certidaoCartorial.certidao accept=\".pdf, .png, .jpg\" ngf-max-size=10MB ngf-model-invalid=errorFile required name=arquivo id=input-file-id> <span class=form-error ng-show=\"uploadCertidaoCartorial.arquivo.$error.required && certidaoCartorialSubmitted\">Campo de preenchimento obrigatório</span><md-button ng-click=incluir() layout-align=right class=\"md-primary md-raised\">Inserir</md-button></div><div layout=row ng-show=\"certidoesCartorial.length > 0\"><md-content style=\"width: 100% !important\"><h4>Documentos cartoriais</h4><table class=mui-table style=\"width:100% !important\"><thead><tr><th style=\"width: 200px !important\">Nome da certidão</th><th>Data da emissão</th><th>Nome do arquivo</th><th>Ação</th></tr></thead><tbody><tr ng-repeat=\"item in certidoesCartorial\" ng-model-options=\"{trackBy: \'$value.id\'}\"><td>{{item.nome}}</td><td>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td>{{item.nomeArquivo}}</td><td><ng-md-icon icon=search style=fill:#788489 size=18 alt=\"Visualizar documento\" ng-click=visualizarCertidao(item.id)></ng-md-icon><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=removerCertidao($index)></ng-md-icon></td></tr></tbody></table></md-content></div><div layout-gt-sm=row layout-margin layout-align=\"end right\"><md-button layout-align=right class=md-primary ng-click=confirmar()>Confirmar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/dados-medidor/templates/dados-medidor.html","<fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">Dados do Medidor</label></legend><ng-form isolate-form name=forms.dadosMedidorForm><div layout=row><md-input-container class=margem-direita-padrao ng-show=!dadosMedidorDisabled() flex=50><label>Tipo de Medidor</label><md-select campo-ajuda=INC_FORM_DADOS_MEDIDOR_TIPO_MED required ng-model-options=\"{trackBy : \'$value.name\'}\" ng-model=dadosMedidor.tipoFornecedor ng-change=pesquisaEmpresa()><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposFornecedores | orderByBr:\'tipoFornecedora\'\" ng-value=item>{{item.tipoFornecedora}}</md-option></md-select></md-input-container><md-input-container ng-show=!dadosMedidorDisabled() flex=50><label>Código do Medidor</label><input campo-ajuda=INC_FORM_DADOS_MEDIDOR_COD_MED required ng-model=dadosMedidor.codigo></md-input-container></div><div layout=row ng-show=!dadosMedidorDisabled()><md-autocomplete campo-ajuda=INC_FORM_DADOS_MEDIDOR_EMP_FORN flex required md-input-name=empresaFornecedora md-item-text=empresa.nome md-items=\"empresa in querySearch(searchText) | orderByBr:\'nome\'\" md-selected-item=dadosMedidor.empresaFornecedora md-search-text=searchText md-min-length=0 md-no-cache=true md-escape-options=clear placeholder=\"Informe o dado\" md-floating-label=\"Empresa Fornecedora\" md-clear-button=!$mdAutocompleteCtrl.isDisabled md-select-on-match md-match-case-insensitive><md-item-template><span md-highlight-text=searchText md-highlight-flags=^i>{{ empresa.nome }}</span></md-item-template><md-not-found>Nenhum resultado para \"{{searchText}}\" foi encontrado.</md-not-found></md-autocomplete></div><div layout=row layout-align=\"end end\"><div style=\"margin-right: 15px\"><md-button ng-show=!dadosMedidorDisabled() ng-click=validarDadosMedidor() md-no-ink class=\"md-raised md-primary\">{{ dadosMedidor.index >= 0 ? \'Alterar Medidor\' : \'Incluir Medidor\' }}</md-button></div></div></ng-form><div layout=column ng-show=\"model.length > 0 || dadosMedidorDisabled()\"><md-table-container><table md-table><thead md-head md-order=ordenacao><tr md-row><th md-column md-order-by=empresaFornecedora.tipoFornecedoraEnum.tipoFornecedora>Tipo de Medidor</th><th md-column md-order-by=codigo>Código do Medidor</th><th md-column md-order-by=empresaFornecedora.nome>Empresa Fornecedora</th><th md-column ng-show=!dadosMedidorDisabled()>Ações</th></tr></thead><tbody md-body><tr md-row ng-show=\"model.length === 0\"><td md-cell colspan=4>Nenhum registro</td></tr><tr md-row ng-repeat=\"dadosMedidor in model | orderBy: ordenacao | limitTo: paginacao.limit: (paginacao.page - 1) * paginacao.limit\"><td md-cell>{{ dadosMedidor.empresaFornecedora.tipoFornecedoraEnum.tipoFornecedora }}</td><td md-cell>{{ dadosMedidor.codigo }}</td><td md-cell>{{ dadosMedidor.empresaFornecedora.nome }}</td><td md-cell ng-show=!dadosMedidorDisabled()><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"excluirDadosMedidor($event, dadosMedidor)\" class=\"md-fab md-raised md-mini\" aria-label=\"Excluir Medidor\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir Medidor</md-tooltip></md-icon></md-button><md-button ng-click=\"editarDadosMedidor($event, dadosMedidor)\" class=\"md-fab md-raised md-mini\" aria-label=\"Editar Medidor\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Medidor</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacao.limit md-limit-options=paginacao.limitOptions md-page=paginacao.page md-total=\"{{ model.length }}\" md-page-select ng-disabled=\"model.length < 6\"></md-table-pagination></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/descricao-terreno-cadastral/templates/descricao-terreno-cadastral.html","<div id=secaoDescricaoCadastral layout=column><div layout=row style=\"margin-bottom: 10px\" layout-margin><md-radio-group class=secaoDescricaoCadastral ng-required=\"!terreno.tipoGeometriaCadastral && $parent.submittedTerreno\" ng-class=\"!terreno.tipoGeometriaCadastral && $parent.submittedTerreno ? \'radio-invalid md-off\' : null\" ng-model=terreno.tipoGeometriaCadastral style=\"width: 100%\" ng-change=alterarTipoDesenho() ng-disabled=\"isDetalhar() || desabilitar\" layout=row ng-click=exibirMsgConfirmacaoEdicao();><div layout=column flex=50><md-radio-button campo-ajuda=\"\" value=ARQUIVO>Arquivo (zip)<md-tooltip class=tooltip><strong>{{msgShapefileTitulo}}</strong><br><span ng-repeat=\"itemShape in msgShapefileLines track by $index\">{{itemShape}}<br></span><br><strong>{{msgTxtCsvTitulo}}</strong><br><span ng-repeat=\"itemTxtCsv in msgTxtCsvLines track by $index\">{{itemTxtCsv}}<br></span></md-tooltip></md-radio-button><div ng-hide=\"terreno.tipoGeometriaCadastral !== \'ARQUIVO\' && isDetalhar()\"><upload-arquivo style=\"margin-top: 30px\" is-disabled=\"terreno.tipoGeometriaCadastral !== \'ARQUIVO\' || desabilitar\" arquivo=terreno.arquivoCadastral municipio=imovel.enderecamento.codMunicipio fn-limpar=limparDadosDescricaoCadastral() fn-salvar=salvarDadosDescricaoCadastral(data) detail=isDetalhar()></upload-arquivo></div></div><div layout=column flex=50><md-radio-button value=VETORIZACAO campo-ajuda=INC_FORM_TERRENO_VETORIZACAO ng-disabled=\"(terreno.tipoGeometriaCartorial === \'ARQUIVO\' && imovel.incorporacao.matriculas.length > 0) || isDetail || desabilitar\">Vetorização em Tela</md-radio-button><label style=\"margin-left: 15px; margin-top: 12px; color: red\" ng-show=\"terreno.tipoGeometriaCadastral === TIPO_DESENHO.VETORIZACAO\">Essa vetorização possui baixa qualidade de precisão.</label></div></md-radio-group></div><md-input-container ng-class=\"forms.terrenoForm.memorialDescritivoCadastral.$error.required && submittedTerreno?\'md-input-invalid\':null\" class=md-block flex-gt-xs><label>Memorial Descritivo</label><textarea campo-ajuda=INC_FORM_TERRENO_MEMO_DESC_CAD ng-model=terreno.memorialDescritivoCadastral name=memorialDescritivoCadastral class=overflow-textarea ng-required=terreno.geometriaTerreno ng-disabled=\"desabilitarMemorialDescritivo() || isDetalhar()\" ng-click=exibirMsgConfirmacaoEdicao();>\n        </textarea></md-input-container><div layout=row><div>Unidade de Medida da Área:</div><md-radio-group campo-ajuda=INC_FORM_TERRENO_UN_MEDIDA_AREA_CAD name=unidadeMedidaCadastral layout=row ng-change=alteraMha() ng-model=terreno.unidadeMedidaCadastral ng-disabled=\"isDetalhar() || desabilitar\" style=\"margin-left: 10px\" ng-change=\"calcularFracaoIdealUniaoCadastral(); calculaFracaoAreaUniao(); calculaFracaoAreaTotal()\" ng-click=exibirMsgConfirmacaoEdicao();><md-radio-button campo-ajuda=INC_FORM_TERRENO_METRO_CAD value=m2>m²</md-radio-button><md-radio-button campo-ajuda=INC_FORM_TERRENO_HECTARE_CAD value=ha>ha</md-radio-button></md-radio-group></div><div layout-gt-xs=row ng-show=\"imovel.terreno.geometriaTerreno || isTipoIncorporacaoFusao()\"><div layout=column flex style=\"margin-top: 20px\" ng-if=\"terreno.arquivoCadastral && !isVetorizacao()\"><div>Área da Geometria do Terreno</div><div campo-ajuda=INC_FORM_TERRENO_AREA_GEOM_TERREN_CAD ng-show=\"terreno.unidadeMedidaCadastral != \'ha\' && terreno.areaTotalCadastral\"><b>{{terreno.areaShapeFileCadastral|number:4|numeroBr}} m²</b></div><div ng-show=\"terreno.unidadeMedidaCadastral == \'ha\' && terreno.areaTotalCadastral\"><b>{{terreno.areaShapeFileCadastral/10000|number:4|numeroBr}} ha</b></div></div><div layout=column flex><md-input-container ng-class=\"forms.terrenoForm.areaTotalCadastral.$error.required && submittedTerreno?\'md-input-invalid\':null\"><label>Área Total</label><input type=text campo-ajuda=INC_FORM_TERRENO_AREA_TOTAL_CAD class=secaoDescricaoCadastral ng-model=terreno.areaTotalCadastral ng-disabled=\"isDetalhar() || isTipoIncorporacaoFusao() || desabilitar\" ng-required=terreno.geometriaTerreno ui-number-mask=\"terreno.unidadeMedidaCadastral == \'ha\' ? 4 : 2\" name=areaTotalCadastral ng-blur=\"validarAreaTotal(); validarAreaUniao(); validarFracaoIdealImovel();\" ng-change=calculaFracaoAreaTotal() ng-click=exibirMsgConfirmacaoEdicao();></md-input-container><div ng-show=\"terreno.unidadeMedidaCadastral == \'ha\' && terreno.areaTotalCadastral\">{{terreno.areaTotalCadastral*10000|number:2|numeroBr}} m²</div><div ng-show=\"terreno.unidadeMedidaCadastral == \'m2\' && terreno.areaTotalCadastral\">{{terreno.areaTotalCadastral/10000|number:4|numeroBr}} ha</div></div><div layout=column flex><md-input-container ng-class=\"forms.terrenoForm.areaUniaoCadastral.$error.required && submittedTerreno ? \'md-input-invalid\': null\"><label>Área da União</label><input campo-ajuda=INC_FORM_TERRENO_AREA_UNIAO_CAD type=text class=secaoDescricaoCadastral ng-disabled=\"isDetalhar() || desabilitar\" ng-model=terreno.areaUniaoCadastral ng-required=\"imovel.terreno.geometriaTerreno && imovel.proprietario.isIntegralmente || isImovelUnidadeAutonoma()\" ui-number-mask=\"terreno.unidadeMedidaCadastral == \'ha\' ? 4 : 2\" name=areaUniaoCadastral ng-blur=\"validarAreaUniao(); validarFracaoIdealImovel()\" ng-change=calculaFracaoAreaUniao() ng-click=exibirMsgConfirmacaoEdicao();></md-input-container><div ng-show=\"terreno.unidadeMedidaCadastral == \'ha\' && terreno.areaUniaoCadastral\">{{terreno.areaUniaoCadastral*10000|number:2|numeroBr}} m²</div><div ng-show=\"terreno.unidadeMedidaCadastral == \'m2\' && terreno.areaUniaoCadastral\">{{terreno.areaUniaoCadastral/10000|number:4|numeroBr}} ha</div></div></div><div layout=row ng-if=imovel.terreno.areaUniaoCadastral><div layout=column flex><md-input-container ng-class=\"forms.terrenoForm.memorialDescritivoCadastralUniao.$error.required && submittedTerreno ? \'md-input-invalid\': null\" class=md-block flex-gt-xs><label>Memorial Descritivo da Área da União</label><textarea campo-ajuda=INC_FORM_TERRENO_MEMO_DESC_UNIAO_CAD ng-disabled=isDetalhar() class=\"secaoDescricaoCadastral overflow-textarea\" ng-model=terreno.memorialDescritivoCadastralUniao name=memorialDescritivoCadastralUniao ng-click=exibirMsgConfirmacaoEdicao(); required>\n                            </textarea></md-input-container></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/descricao-terreno-cartorial/templates/descricao-terreno-cartorial.html","<div layout=column><div layout=row layout-margin><md-radio-group class=secaoDescricaoCartorial ng-disabled=\"isDetalhar() || desabilitar\" ng-model=terreno.tipoGeometriaCartorial style=\"width: 100%\" ng-change=alterarTipoDesenho() layout=row ng-click=exibirMsgConfirmacaoEdicao();><div layout=column flex=50><md-radio-button id=selecionarArquivoShape campo-ajuda=INC_FORM_TERRENO_ARQ_CART ng-value=TIPO_DESENHO.ARQUIVO>Arquivo (zip, csv e txt)<md-tooltip class=tooltip><strong>{{msgShapefileTitulo}}</strong><br><span ng-repeat=\"itemShape in msgShapefileLines track by $index\">{{itemShape}}<br></span><br><strong>{{msgTxtCsvTitulo}}</strong><br><span ng-repeat=\"itemTxtCsv in msgTxtCsvLines track by $index\">{{itemTxtCsv}}<br></span></md-tooltip></md-radio-button><div ng-hide=\"terreno.tipoGeometriaCartorial !== \'ARQUIVO\' && isDetalhar()\"><upload-arquivo style=\"margin-top: 30px\" is-disabled=\"terreno.tipoGeometriaCartorial !== \'ARQUIVO\'\" arquivo=terreno.arquivoCartorial municipio=imovel.enderecamento.codMunicipio fn-limpar=limparDadosDescricaoCartorial() fn-salvar=salvarDadosDescricaoCartorial(data)></upload-arquivo></div></div><div layout=column flex=50><md-radio-button campo-ajuda=INC_FORM_TERRENO_DESCR_NAO_GEO ng-value=TIPO_DESENHO.NAO_GEORREFERENCIADA>Descrição Não Georreferenciada</md-radio-button></div></md-radio-group></div><div layout=row layout-margin layout-padding><md-checkbox campo-ajuda=INC_FORM_TERRENO_LEVANT_INCRA class=secaoDescricaoCartorial ng-model=terreno.isCertificaoIncra ng-disabled=\"isDetalhar() || desabilitar\" ng-if=\"imovel.naturezaImovel.id === 2\" ng-click=exibirMsgConfirmacaoEdicao();>Levantamento cadastral certificado pelo Incra</md-checkbox></div><div ng-show=\"isNaoGeoreferenciada() || terreno.geometriaTerrenoCartorial\"><md-input-container ng-class=\"forms.terrenoForm.memorialDescritivoCartorial.$error.required && submittedTerreno ? \'md-input-invalid\': null\" class=md-block flex-gt-xs ng-click=exibirMsgConfirmacaoEdicao();><label>Memorial Descritivo</label><textarea campo-ajuda=INC_FORM_TERRENO_MEMO_DESC_CART class=overflow-textarea ng-model=terreno.memorialDescritivoCartorial name=memorialDescritivoCartorial style=\"min-height: 30px\" ng-required=terreno.geometriaTerrenoCartorial ng-disabled=isDetalhar() ng-click=exibirMsgConfirmacaoEdicao();>\n      </textarea></md-input-container><div layout=row ng-disabled=!isNaoGeoreferenciada()><div>Unidade de Medida da Área:</div><md-radio-group campo-ajuda=INC_FORM_TERRENO_UN_MEDIDA_AREA_CART name=unidadeMedidaCartorial layout=row ng-change=alteraMha() ng-disabled=\"isDetalhar() || desabilitar\" ng-model=terreno.unidadeMedidaCartorial style=\"margin-left: 10px\" ng-change=calcularFracaoIdealUniaoCartorial() ng-click=exibirMsgConfirmacaoEdicao();><md-radio-button campo-ajuda=INC_FORM_TERRENO_METRO_CART value=m2>m²</md-radio-button><md-radio-button campo-ajuda=INC_FORM_TERRENO_HECTARE_CART value=ha>ha</md-radio-button></md-radio-group></div><div layout-gt-xs=row><div layout=column flex style=\"margin-top: 20px\" ng-if=\"terreno.arquivoCartorial && !isNaoGeoreferenciada()\"><div>Área da Geometria do Terreno</div><div campo-ajuda=INC_FORM_TERRENO_AREA_GEOM_TERREN_CART ng-show=\"terreno.unidadeMedidaCartorial != \'ha\' && terreno.areaTotalCartorial\"><b>{{terreno.areaShapeFileCartorial|number:2|numeroBr}} m²</b></div><div ng-show=\"terreno.unidadeMedidaCartorial == \'ha\' && terreno.areaTotalCartorial\"><b>{{terreno.areaTotalCartorial/10000|number:4|numeroBr}} ha</b></div></div><div layout=column flex><md-input-container ng-class=\"forms.terrenoForm.areaTotalCartorial.$error.required && submittedTerreno?\'md-input-invalid\':null\"><label>Área Total</label><input campo-ajuda=INC_FORM_TERRENO_AREA_TOTAL_CART type=text class=secaoDescricaoCartorial ng-disabled=\"isDetalhar() || desabilitar\" ng-model=terreno.areaTotalCartorial ng-required=terreno.geometriaTerrenoCartorial ui-number-mask=\"terreno.unidadeMedidaCartorial == \'ha\' ? 4 : 2\" name=areaTotalCartorial ng-blur=\"validarAreaTotal(); validarAreaUniao(); validarFracaoIdealImovel()\" ng-click=exibirMsgConfirmacaoEdicao();></md-input-container><!--calculo ha AQUI--><div ng-show=\"terreno.unidadeMedidaCartorial == \'ha\' && terreno.areaTotalCartorial\">{{terreno.areaTotalCartorial*10000|number:2|numeroBr}} m²<!--troquei number:2 por 4--></div><div ng-show=\"terreno.unidadeMedidaCartorial == \'m2\' && terreno.areaTotalCartorial\">{{terreno.areaTotalCartorial/10000|number:4|numeroBr}} ha</div></div></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/documentos-cartoriais/templates/documentos-cartoriais.html","<fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">Documentos Cartoriais</label></legend><div layout=column flex><div layout=row><div layout=column flex=50><md-input-container class=md-block flex=100><label>Nome</label><input type=text ng-model=dados.nome></md-input-container></div><div layout=column flex=50><md-input-container class=md-block flex=100><label>Cartório</label><md-select class=secaoDadosIncorporacao ng-model=dados.cartorio ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option ng-repeat=\"item in imovel.incorporacao.matriculas\" ng-value=item.cartorio>{{item.cartorio.nome}}</md-option></md-select></md-input-container></div></div><div layout=row><md-input-container flex=100><ci-input-file model=dados.arquivo></ci-input-file></md-input-container></div><div layout=row layout-align=\"end center\"><md-button class=\"md-primary md-raised primeira-maiuscula\" ng-click=adicionaDocumento()>Adicionar</md-button></div><div class=mgt-25 layout-gt-xs=row layout-align=center ng-if=\"imovel.listaDocumentosCartoriais.length > 0\" flex=100><md-content style=\"width: 100%\"><md-table-container><table md-table md-progress=promise style=\"width: 100%\"><thead md-head><tr md-row><th md-column style=\"width: 15%;text-align: center !important\">Nome</th><th md-column style=\"width: 45%;text-align: center !important\">Cartorio</th><th md-column style=\"width: 25%;text-align: center !important\">Arquivo</th><th md-column style=\"width: 15%;text-align: center !important\"></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in imovel.listaDocumentosCartoriais\"><td md-cell style=\"text-align: center !important\">{{documento.nome}}</td><td md-cell style=\"text-align: center !important\">{{documento.cartorio.nome}}</td><td md-cell style=\"text-align: center !important\">{{documento.arquivo.name}}</td><td md-cell style=\"text-align: center !important\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=Excluir ng-click=\"confirmaRemocao($event, documento)\"><md-tooltip>Excluir Arquivo</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabelaMatriculas.limit md-limit-options=tabelaMatriculas.limitsPage md-page=tabelaMatriculas.page md-total={{imovel.incorporacao.matriculas.length}} md-on-paginate=true></md-table-pagination></md-content></div></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/encargos-fieldset/templates/encargos-fieldset.html","<fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">Encargos *</label></legend><div layout-gt-xs=row><md-input-container class=md-block flex=100 md-is-error=submittedEncargos><label>Encargo</label><textarea campo-ajuda=INC_FORM_TITULO_AQUIS_ENCARGO ng-model=encargo.descricao name=encargo md-maxlength=1000 maxlength=1000 ng-disabled=isDetalhar()>\n                </textarea></md-input-container></div><div layout-gt-xs=row><md-input-container flex=50><label>Prazo para cumprimento *</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_TITULO_AQUIS_PRAZO_CUMP flex ng-model=encargo.prazoCumprimento ng-disabled=\"checkboxData.valor || isDetalhar()\" name=prazoCumprimento inc-ajusta-tamanho></md-datepicker></md-input-container><md-input-container class=md-block flex=50><md-checkbox campo-ajuda=INC_FORM_TITULO_AQUIS_UTIL_VIG_DT_CUMP ng-model=checkboxData.valor ng-value=true ng-change=utilizarDataFinalParaCumprimento(); ng-disabled=isDetalhar()>Utilizar a data de final de vigência do contrato como data para cumprimento</md-checkbox></md-input-container></div><div layout=column flex=50><div campo-ajuda=INC_FORM_TITULO_AQUIS_CUMPR_ENCARGO ng-class=\"!encargo.cumprimento && submittedEncargos? \'input-invalid\' : null\" layout-margin>Cumprimento do Encargo *</div><md-radio-group ng-class=\"!encargo.cumprimento && submittedEncargos? \'radio-invalid\' : null\" ng-model=encargo.cumprimento ng-disabled=isDetalhar()><div layout=row style=\"margin: 0 20px\"><md-radio-button campo-ajuda=INC_FORM_TITULO_AQUIS_PENDENTE value=Pendente flex>Pendente</md-radio-button><md-radio-button campo-ajuda=INC_FORM_TITULO_AQUIS_ATENDIDO value=Atendido flex>Atendido</md-radio-button></div></md-radio-group></div><div flex><md-input-container class=md-block><md-button class=\"md-primary md-raised primeira-maiuscula\" ng-click=adicionarEncargo(); ng-if=!isDetalhar()>{{botaoEncargo}}</md-button></md-input-container></div><div class=mgt-25 layout-gt-xs=row><md-content flex-xs=100 flex=100 ng-if=\"titulo.encargos.length > 0\"><md-table-container><table md-table><thead md-head md-order=ordenacao><tr md-row><th md-column md-order-by=descricao>Encargo</th><th md-column md-order-by=prazoCumprimento>Prazo para cumprimento</th><th md-column md-order-by=cumprimento>Cumprimento</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"encargo in titulo.encargos | orderBy: ordenacao | limitTo: paginacao.limit: (paginacao.page - 1) * paginacao.limit\"><td md-cell>{{encargo.descricao}}</td><td md-cell>{{encargo.prazoCumprimento | date:\"dd/MM/yyyy\"}}</td><td md-cell>{{encargo.cumprimento}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"excluirEncargo($event, encargo)\" class=\"md-fab md-raised md-mini\" aria-label=\"Remover Encargo\" ng-if=!isDetalhar()><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Remover Encargo</md-tooltip></md-icon></md-button><md-button ng-click=editarEncargo(encargo) class=\"md-fab md-raised md-mini\" aria-label=\"Editar Encargo\" ng-if=!isDetalhar()><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Encargo</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacao.limit md-limit-options=paginacao.limitOptions md-page=paginacao.page md-total=\"{{ titulo.encargos.length }}\" md-page-select ng-disabled=\"titulo.encargos.length < 6\"></md-table-pagination></md-content></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/fieldset-titulo-aquisitivo/templates/fieldset-titulo-aquisitivo.html","<fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">Título Aquisitivo</label></legend><div layout=column ng-if=!isDetalhar()><div layout-gt-xs=row flex=50><md-input-container class=md-block flex=50><md-button class=\"md-primary md-raised primeira-maiuscula\" ng-click=exibirTabTituloAquisitivo(); aria-label=\"upload arquivo\">Adicionar Título Aquisitivo</md-button></md-input-container></div></div><div class=mgt-25 flex=100 ng-if=\"ctrlDadosIncorporacao.titulos.length > 0\"><md-table-container><table md-table><thead md-head md-order=ordenacao><tr md-row><th md-column>Tipo</th><th md-column md-order-by=nome>Nome</th><th md-column md-order-by=dataAssinatura>Assinatura</th><th md-column>Encargos</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in ctrlDadosIncorporacao.titulos | orderBy: ordenacao | limitTo: paginacao.limit: (paginacao.page - 1) * paginacao.limit\"><td md-cell>{{ item.tipoAtoLegal.descricao != undefined ? getDescricao(item) : item.tipoTituloAquisitivo.descricao }}</td><td md-cell>{{ item.nome }}</td><td md-cell>{{ item.dataAssinatura | date:\"dd/MM/yyyy\" }}</td><td md-cell>{{ item.encargos.length > 0 ? \"Sim\" : \"Não\" }}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"confirmarExclusaoTitulo(item, $event)\" class=\"md-fab md-raised md-mini\" aria-label=\"Remover Título\" ng-if=!isDetalhar()><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Remover Título</md-tooltip></md-icon></md-button><md-button ng-click=exibirTabTituloAquisitivo(item) class=\"md-fab md-raised md-mini\" aria-label=\"Editar Titulo\"><md-icon ng-bind=\"\'create\'\" ng-if=!isDetalhar()><md-tooltip>Editar Titulo</md-tooltip></md-icon><md-icon ng-bind=\"\'search\'\" ng-if=isDetalhar()><md-tooltip>Detalhar Titulo</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacao.limit md-limit-options=paginacao.limitOptions md-page=paginacao.page md-total=\"{{ ctrlDadosIncorporacao.titulos.length }}\" md-page-select ng-disabled=\"ctrlDadosIncorporacao.titulos.length < 6\"></md-table-pagination></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/localizacao/templates/inc-informar-coordenadas.html","<span><label class=span-original-component>Sistema de Referência:</label><span class=fonteNegrito>{{ imovel.sistemaReferencia.descricao }}</span><br></span><!--<div layout=\"row\">\n    <md-input-container class=\"md-block\" flex>\n      <label id=\"sistemaReferenciaLabel\">Sistema de Referência</label>\n      <md-autocomplete\n        campo-ajuda=\"INC_FORM_LOC_SISTEMA_REFERENCIA\"\n        name=\"sistemaReferencia\"\n        ng-disabled=\"isSistemaReferenciaDisabled()\"\n        md-clear-button=\"!$mdAutocompleteCtrl.isDisabled\"\n        md-selected-item=\"imovel.sistemaReferencia\"\n        md-search-text=\"$scope.searchSistemaReferencia\"\n        md-items=\"item in sistemasReferencia| filter:{descricao:$scope.searchSistemaReferencia} | orderByBr:\'descricao\'\"\n        md-item-text=\"item.descricao\"\n        md-min-length=\"0\"\n        md-escape-options=\"clear\"\n        md-select-on-match\n        md-match-case-insensitive\n        placeholder=\"Informe o dado\"\n        input-aria-labelledby=\"sistemaReferenciaLabel\"\n      >\n        <md-item-template>\n          <span\n            md-highlight-text=\"$scope.searchSistemaReferencia\"\n            md-highlight-flags=\"^i\"\n            >{{item.descricao}}</span\n          >\n        </md-item-template>\n        <md-not-found>\n          Nenhum resultado para \"{{$scope.searchSistemaReferencia}}\" foi\n          encontrado.\n        </md-not-found>\n      </md-autocomplete>\n    </md-input-container>\n  </div>--><div layout=column ng-show=possuiSistemaReferencia()><ng-form id=coordenadasForm name=coordenadasForm layout=column isolate-form><div layout=column><p campo-ajuda=INC_FORM_LOC_FORMATO_COORDENADAS ng-class=\"!imovel.formatoCoordenadas && submittedCoodenadas ? \'input-invalid\' : \'\'\">Formato das Coordenadas *</p><md-radio-group id=formatoCoordenadas ng-class=\"!imovel.formatoCoordenadas && submittedCoodenadas? \'radio-invalid md-off\' : \'\'\" ng-model=imovel.formatoCoordenadas name=formatoCoordenadas layout=row ng-required=true style=\"margin: 0 20px\"><md-radio-button campo-ajuda=INC_FORM_LOC_GRAU_MINUTO_SEGUNDO value=GMS>Grau, minuto e segundo</md-radio-button><md-radio-button campo-ajuda=INC_FORM_LOC_GRAU_DECIMAL value=GD>Grau decimal</md-radio-button><md-radio-button campo-ajuda=INC_FORM_LOC_METRICAS value=M>Métricas</md-radio-button></md-radio-group></div><div layout=column ng-show=\"imovel.formatoCoordenadas === \'GMS\'\"><div layout=row><span campo-ajuda=INC_FORM_LOC_LATITUDE ng-class=\"!grauMinutoSegundo.latitude.hemisferio && submittedCoodenadas ? \'input-invalid\' : null\" layout=row layout-align=\"center center\" flex>Latitude *</span><md-radio-group id=gmsLatHemisferio ng-class=\"!grauMinutoSegundo.latitude.hemisferio && submittedCoodenadas ? \'radio-invalid md-off\' : null\" ng-disabled=\"isGrauMinutoSegundoDisabled() || desabilitar\" ng-model=grauMinutoSegundo.latitude.hemisferio name=gmsLatHemisferio layout=row layout-align=\"start center\" ng-required=\"imovel.formatoCoordenadas === \'GMS\'\" style=\"margin-right: 20px\" flex><md-radio-button campo-ajuda=INC_FORM_LOC_LATITUDE_NORTE value=N>Norte</md-radio-button><md-radio-button campo-ajuda=INC_FORM_LOC_LATITUDE_SUL value=S>Sul</md-radio-button></md-radio-group><md-input-container class=margem-direita-padrao flex=20><label>Graus°</label><input id=gmsLatGraus campo-ajuda=INC_FORM_LOC_LATITUDE_GRAUS ng-disabled=\"isGrauMinutoSegundoDisabled() || desabilitar\" type=text ng-model=grauMinutoSegundo.latitude.graus name=gmsLatGraus ng-required=\"imovel.formatoCoordenadas === \'GMS\'\" ui-number-mask=0 ui-hide-group-sep></md-input-container><md-input-container class=margem-direita-padrao flex=20><label>Minutos\'</label><input id=gmsLatMinutos campo-ajuda=INC_FORM_LOC_LATITUDE_MINUTOS ng-disabled=\"isGrauMinutoSegundoDisabled() || desabilitar\" type=text ng-model=grauMinutoSegundo.latitude.minutos name=gmsLatMinutos ng-required=\"imovel.formatoCoordenadas === \'GMS\'\" ui-number-mask=0 ui-hide-group-sep></md-input-container><md-input-container flex=20><label>Segundos\"</label><input id=gmsLatSegundos campo-ajuda=INC_FORM_LOC_LATITUDE_SEGUNDOS ng-disabled=\"isGrauMinutoSegundoDisabled() || desabilitar\" type=text ng-model=grauMinutoSegundo.latitude.segundos name=gmsLatSegundos ng-required=\"imovel.formatoCoordenadas === \'GMS\'\" ui-number-mask=2 ui-hide-group-sep></md-input-container></div><div layout=row><span campo-ajuda=INC_FORM_LOC_LONGITUDE ng-class=\"!grauMinutoSegundo.longitude.hemisferio && submittedCoodenadas ? \'input-invalid\' : null\" layout=row layout-align=\"center center\" flex>Longitude *</span><md-radio-group id=gmsLongHemisferio ng-class=\"!grauMinutoSegundo.longitude.hemisferio && submittedCoodenadas ? \'radio-invalid md-off\' : null\" ng-disabled=\"isGrauMinutoSegundoDisabled() || desabilitar\" ng-model=grauMinutoSegundo.longitude.hemisferio name=gmsLongHemisferio layout=row layout-align=\"start center\" ng-required=\"imovel.formatoCoordenadas === \'GMS\'\" style=\"margin-right: 20px\" flex><md-radio-button campo-ajuda=INC_FORM_LOC_LONGITUDE_OESTE value=O>Oeste</md-radio-button><md-radio-button campo-ajuda=INC_FORM_LOC_LONGITUDE_LESTE value=L>Leste</md-radio-button></md-radio-group><md-input-container class=margem-direita-padrao flex=20><label>Graus°</label><input id=gmsLongGraus campo-ajuda=INC_FORM_LOC_LONGITUDE_GRAUS ng-disabled=\"isGrauMinutoSegundoDisabled() || desabilitar\" type=text ng-model=grauMinutoSegundo.longitude.graus name=gmsLongGraus ng-required=\"imovel.formatoCoordenadas === \'GMS\'\" ui-number-mask=0 ui-hide-group-sep></md-input-container><md-input-container class=margem-direita-padrao flex=20><label>Minutos\'</label><input id=gmsLongMinutos campo-ajuda=INC_FORM_LOC_LONGITUDE_MINUTOS ng-disabled=\"isGrauMinutoSegundoDisabled() || desabilitar\" type=text ng-model=grauMinutoSegundo.longitude.minutos name=gmsLongMinutos ng-required=\"imovel.formatoCoordenadas === \'GMS\'\" ui-number-mask=0 ui-hide-group-sep></md-input-container><md-input-container flex=20><label>Segundos\"</label><input campo-ajuda=INC_FORM_LOC_LONGITUDE_SEGUNDOS ng-disabled=\"isGrauMinutoSegundoDisabled() || desabilitar\" type=text ng-model=grauMinutoSegundo.longitude.segundos name=gmsLongSegundos ng-required=\"imovel.formatoCoordenadas === \'GMS\'\" ui-number-mask=2 ui-hide-group-sep></md-input-container></div></div><div layout=column ng-show=\"imovel.formatoCoordenadas === \'GD\'\"><div layout=row><span campo-ajuda=INC_FORM_LOC_LATITUDE layout=row layout-align=\"center center\" flex>Latitude</span><md-radio-group ng-disabled=\"isGrauDecimalDisabled() || desabilitar\" ng-model=grauDecimal.latitude.hemisferio name=gdLatHemisferio layout=row layout-align=\"start center\" ng-required=\"imovel.formatoCoordenadas === \'GD\'\" ng-change=onChangeHemisferioLatitude() style=\"margin-right: 20px\" flex><md-radio-button campo-ajuda=INC_FORM_LOC_LATITUDE_NORTE value=N>Norte</md-radio-button><md-radio-button campo-ajuda=INC_FORM_LOC_LATITUDE_SUL value=S>Sul</md-radio-button></md-radio-group><md-input-container flex=60><label>Decimal</label><input campo-ajuda=INC_FORM_LOC_LATITUDE_DECIMAL ng-disabled=\"isGrauDecimalDisabled() || desabilitar\" type=text ng-model=grauDecimal.latitude.decimal name=gdLatDecimal ng-required=\"imovel.formatoCoordenadas === \'GD\'\" ui-number-mask=8 ui-hide-group-sep ui-negative-number></md-input-container></div><div layout=row><span campo-ajuda=INC_FORM_LOC_LONGITUDE layout=row layout-align=\"center center\" flex>Longitude</span><md-radio-group ng-disabled=\"isGrauDecimalDisabled() || desabilitar\" ng-model=grauDecimal.longitude.hemisferio name=gdLongHemisferio layout=row layout-align=\"start center\" ng-required=\"imovel.formatoCoordenadas === \'GD\'\" ng-change=onChangeHemisferioLongitude() style=\"margin-right: 20px\" flex><md-radio-button campo-ajuda=INC_FORM_LOC_LONGITUDE_OESTE value=O>Oeste</md-radio-button><md-radio-button campo-ajuda=INC_FORM_LOC_LONGITUDE_LESTE value=L>Leste</md-radio-button></md-radio-group><md-input-container flex=60><label>Decimal</label><input campo-ajuda=INC_FORM_LOC_LONGITUDE_DECIMAL ng-disabled=\"isGrauDecimalDisabled() || desabilitar\" type=text ng-model=grauDecimal.longitude.decimal name=gdLongDecimal ng-required=\"imovel.formatoCoordenadas === \'GD\'\" ui-number-mask=8 ui-hide-group-sep ui-negative-number></md-input-container></div></div><div layout=row ng-show=\"imovel.formatoCoordenadas === \'M\'\"><md-input-container class=margem-direita-padrao flex><label>Coordenada E</label><input campo-ajuda=INC_FORM_LOC_COORDENADA_E ng-disabled=\"isMetricasDisabled() || desabilitar\" type=text ng-model=metricas.coordenadaE name=metricasCoordenadaE ng-required=\"imovel.formatoCoordenadas === \'M\'\" ui-number-mask=4 ui-hide-group-sep></md-input-container><md-input-container class=margem-direita-padrao flex><label>Coordenada N</label><input campo-ajuda=INC_FORM_LOC_COORDENADA_N ng-disabled=\"isMetricasDisabled() || desabilitar\" type=text ng-model=metricas.coordenadaN name=metricasCoordenadaN ng-required=\"imovel.formatoCoordenadas === \'M\'\" ui-number-mask=4 ui-hide-group-sep></md-input-container><md-input-container flex><label id=metricasFusoUTMLabel>Fuso UTM</label><md-select campo-ajuda=INC_FORM_LOC_FUSO_UTM ng-model=metricas.fusoUTM ng-required=true ng-model-options=\"{trackBy: \'$value.name\'}\" name=metricasFusoUTM ng-disabled=\"isMetricasDisabled() || desabilitar\"><md-option ng-repeat=\"item in fusosUTM | orderByBr2:\'zona\':\'hemisferio\'\" ng-value=item>{{item.zona.concat(\' \').concat(item.hemisferio)}}</md-option></md-select></md-input-container></div><div layout=row layout-align=\"end center\"><md-button ng-show=!esconderBotaoMarcarPonto() class=\"md-primary md-raised primeira-maiuscula\" ng-click=marcarPonto()>Marcar Ponto</md-button></div></ng-form></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/localizacao/templates/inc-localizacao-imovel.html","<div layout=column><inc-mensagem tipo=alerta no-icon>Usuário Logado: {{usuario.nome}} - {{usuario.cpf}}<br>UG Executora do Titular: {{usuario.idOrganizacao}} - {{usuario.organizacao}}<br>Você pode cadastrar imóveis para: {{getProprietariosCadastro()}}<br>Locais permitidos: {{usuario.jurisdicoes.sort().toString()}}</inc-mensagem><md-input-container name=tipoImovelContainer class=md-block flex-gt-sm><label>Tipo de Imóvel</label><md-select campo-ajuda=INC_FORM_LOC_TIPO_IMOVEL required ng-model=imovel.tipoImovel ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoImovel ng-class=\"isDadoAlterado(\'imovel.tipoImovel.id\')\" ng-change=\"atualizaTipoImovel(); handleTipoImovelSelecionado()\" ng-disabled=\"isTipoImovelDisabled() || desabilitar\"><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposImoveis | filter : isNotTipoUnidadeAutonomaPrimeiraParte\" ng-value=item ng-disabled=!habilitaOpcaoGleba>{{item.descricao}}</md-option><md-optgroup label=\"Unidade Autônoma\"><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposImoveis | filter : isTipoUnidadeAutonoma\" ng-value=item ng-disabled=!habilitaDemaisOpcoes>{{item.descricao}}</md-option></md-optgroup><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposImoveis | filter : isNotTipoUnidadeAutonomaSegundaParte\" ng-value=item ng-disabled=!habilitaDemaisOpcoes>{{item.descricao}}</md-option></md-select></md-input-container><v-accordion id=accordion multiple class=vAccordion--default control=accordion><v-pane id=dadosLocalizacaoImovel expanded><v-pane-header>Dados de Localização do Imóvel</v-pane-header><v-pane-content id=dadosLocalizacaoImovelContent><div layout=column><div layout=row><div layout=column flex=40><inc-mensagem id=mensagemPontoObrigatorio titulo=\"Ponto do Imóvel Obrigatório\" tipo=info>Para o cadastro do imóvel é necessário marcar um ponto na testada principal do imóvel. Clique no local desejado no mapa abaixo para marcar a localização.</inc-mensagem><div layout=row class=mapaLocalizacao><ci-mapa-incorporacao showminimapa=true heightminimapa=100 widthminimapa=100 tabindex=-1 imovel=imovel action={{action}} habilitar-desenho=false edificacoes=edificacoesUnidadeAutonoma></ci-mapa-incorporacao></div></div><div layout-xs=column flex=60><v-accordion id=my-accordion multiple class=vAccordion--default control=accordion><v-pane id=coordenadasDoPonto expanded><v-pane-header>Coordenadas do Ponto</v-pane-header><v-pane-content id=coordenadasDoPontoContent><inc-informar-coordenadas imovel=imovel action={{action}} desabilitar=desabilitar></inc-informar-coordenadas></v-pane-content></v-pane><v-pane id=enderecoDoImovel expanded><v-pane-header>Endereço do Imóvel</v-pane-header><v-pane-content><div><div layout-gt-xs=row><md-input-container class=\"md-block margem-direita-padrao\" flex-gt-sm><label>País</label><md-select campo-ajuda=INC_FORM_LOC_PAIS ng-required=!isImovelEspelhoDagua() ng-model=imovel.enderecamento.pais ng-model-options=\"{trackBy: \'$value.id\'}\" name=pais ng-change=buscaCidadesPorPais() ng-disabled=\"isPaisDisabled() || desabilitar\" ng-class=\"isDadoAlterado(\'imovel.enderecamento.pais.id\')\"><md-option ng-repeat=\"item in paises| orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div layout=row flex-gt-xs ng-show=\"isPaisBrasil() && exibeCep()\"><md-input-container class=\"md-block padding-padrao-interface\" flex><label>CEP<md-icon ng-bind=\"\'info_outline\'\" ng-if=editandoUA()><md-tooltip>Dados atualizados nas demais UAs</md-tooltip></md-icon></label><input campo-ajuda=INC_FORM_LOC_CEP campo-ajuda=INC_FORM_LOC_CEP ui-br-cep-mask ng-model=imovel.enderecamento.cep ng-required=\"isPaisBrasil() && !isImovelEspelhoDaguaOuCavidade()\" ng-change=buscaEnderecoPorCep() name=cep ng-class=\"isDadoAlterado(\'imovel.enderecamento.cep\')\" ng-disabled=\"isCepDisabled() || desabilitar\" ng-click=exibirMsgConfirmacaoEdicao();></md-input-container><div style=\"margin: 33px 5px\"><a href=http://www.buscacep.correios.com.br/sistemas/buscacep/ target=_blank>Não sei o CEP</a></div></div></div><div layout-gt-xs=row class=tamanho-container ng-show=\"isPaisBrasil() && imovel.enderecamento.cep\"><md-input-container ng-if=enderecoDisabled class=\"md-block padding-padrao-interface margem-baixa-padrao\" flex-gt-sm flex=33><label>UF</label><input campo-ajuda=INC_FORM_LOC_UF ng-model=imovel.enderecamento.uf ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-disabled=\"enderecoDisabled || desabilitar\" ng-class=\"isDadoAlterado(\'imovel.enderecamento.uf\')\"></md-input-container><md-input-container ng-if=!enderecoDisabled class=\"md-block padding-padrao-interface margem-baixa-padrao\" flex-gt-sm flex=33><label>UF</label><md-select campo-ajuda=INC_FORM_LOC_UF select-clear ng-model=imovel.enderecamento.uf ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-disabled=\"false || desabilitar\" ng-change=buscarMunicipiosPorUf(imovel.enderecamento.uf) ng-class=\"isDadoAlterado(\'imovel.enderecamento.uf\')\"><md-option ng-repeat=\"uf in ufs\" value={{uf.sigla}}>{{uf.sigla}}</md-option></md-select></md-input-container><md-input-container ng-if=\"!isImovelEspelhoDaguaOuCavidade() && imovel.enderecamento.municipio!=\'\'\" class=\"md-block padding-padrao-interface margem-baixa-padrao\" flex=33 flex-gt-xs><label>Município</label><input campo-ajuda=INC_FORM_LOC_MUNICIPIO ng-model=imovel.enderecamento.municipio ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-disabled=\"enderecoDisabled || desabilitar\" placeholder=Município ng-class=\"isDadoAlterado(\'imovel.enderecamento.municipio\')\"></md-input-container><md-input-container ng-if=\"!isImovelEspelhoDaguaOuCavidade() && imovel.enderecamento.municipio==\'\'\" class=\"md-block padding-padrao-interface margem-baixa-padrao\" flex=33 flex-gt-xs><label id=municipioLabel>Município</label><md-autocomplete md-no-cache=true campo-ajuda=INC_FORM_LOC_MUNIC name=municipio ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" md-clear-button=true md-selected-item-change=\"setMunicipio(municipio.nome, municipio)\" ng-selected=municipio.nome md-selected-item=municipio.nome md-search-text=vm.searchMunicipio md-items=\"municipio in municipios | filtro: \'nome\' : vm.searchMunicipio | orderByBr:\'nome\'\" md-item-text=municipio.nome md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o Município\" input-aria-labelledby=municipioLabel><md-item-template><span md-highlight-text=vm.searchMunicipio md-highlight-flags=^i>{{municipio.nome}}</span></md-item-template><!-- <md-not-found>Nenhum resultado encontrado.</md-not-found> --></md-autocomplete></md-input-container><md-input-container class=md-block flex=33 flex-gt-sm ng-show=\"isPaisBrasil() && imovel.enderecamento.cep\"><label>Tipo de Logradouro<md-icon ng-bind=\"\'info_outline\'\" ng-if=editandoUA()><md-tooltip>Dados atualizados nas demais UAs</md-tooltip></md-icon></label><md-select campo-ajuda=INC_FORM_LOC_TIPO_LOGRADOURO ng-model=imovel.enderecamento.tipoLogradouro ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-disabled=\"isTipoLogradouroDisabled() || desabilitar\" ng-class=\"isDadoAlterado(\'imovel.enderecamento.tipoLogradouro\')\"><md-option ng-repeat=\"logradouro in tiposLogradouro\" value={{logradouro.descricao}}>{{logradouro.descricao}}</md-option></md-select></md-input-container></div><div layout=column ng-if=isImovelEspelhoDaguaOuCavidade()><md-dialog-content flex=100><md-input-container ng-if=!imovel.enderecamento.cep flex=40><label>Unidade Federativa</label><md-select campo-ajuda=INC_FORM_LOC_UNID_FED select-clear ng-model=imovel.enderecamento.uf ng-required=isImovelEspelhoDaguaOuCavidade() ng-disabled=\"false || desabilitar\" ng-change=buscarMunicipiosPorUf(imovel.enderecamento.uf) ng-class=\"isDadoAlterado(\'imovel.enderecamento.uf\')\"><md-option ng-repeat=\"uf in ufs\" value={{uf.sigla}}>{{uf.sigla}}</md-option></md-select></md-input-container><md-input-container flex=60 flex-gt-sm ng-if=isInclusao()><label id=municipioLabel>Município</label><md-autocomplete md-no-cache=true campo-ajuda=INC_FORM_LOC_MUNIC name=municipio ng-required=isImovelEspelhoDaguaOuCavidade() md-clear-button=true md-selected-item-change=\"setMunicipio(municipio.nome, municipio)\" ng-selected=municipio.nome md-selected-item=municipio.nome md-search-text=vm.searchMunicipio md-items=\"municipio in municipios | filtro: \'nome\' : vm.searchMunicipio | orderByBr:\'nome\'\" md-item-text=municipio.nome md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o Município\" input-aria-labelledby=municipioLabel><md-item-template><span md-highlight-text=vm.searchMunicipio md-highlight-flags=^i>{{municipio.nome}}</span></md-item-template><!-- <md-not-found>Nenhum resultado encontrado.</md-not-found> --></md-autocomplete></md-input-container><md-input-container flex=60 ng-if=!isInclusao()><label>Município</label><input campo-ajuda=INC_FORM_LOC_MUNICIPIO ng-model=imovel.enderecamento.municipio ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-disabled=\"false || desabilitar\" placeholder=Município ng-class=\"isDadoAlterado(\'imovel.enderecamento.municipio\')\"></md-input-container></md-dialog-content></div><div layout-gt-sm=row class=tamanho-container ng-show=\"isPaisBrasil() && imovel.enderecamento.cep\"><md-input-container class=\"md-block padding-padrao-interface margem-top-padrao margem-baixa-padrao\" flex=33 flex-gt-xs><label>Logradouro<md-icon ng-bind=\"\'info_outline\'\" ng-if=editandoUA()><md-tooltip>Dados atualizados nas demais UAs</md-tooltip></md-icon></label><input campo-ajuda=INC_FORM_LOC_LOGRADOURO ng-model=imovel.enderecamento.logradouro ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-disabled=\"isLogradouroDisabled() || desabilitar\" ng-class=\"isDadoAlterado(\'imovel.enderecamento.logradouro\')\"></md-input-container><md-input-container class=\"md-block padding-padrao-interface margem-top-padrao margem-baixa-padrao\" flex=33 flex-gt-sm><input campo-ajuda=INC_FORM_LOC_NUMERO ng-model=imovel.numero name=numero ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" placeholder=Número ng-pattern=/^(([0-9][0-9]*)|([sS][\\/][nN]))$/ ng-class=\"isDadoAlterado(\'imovel.numero\')\" ng-disabled=\"isNumeroDisabled() || desabilitar\"></md-input-container><md-input-container class=md-block flex=33 flex-gt-xs><label>Bairro/Distrito<md-icon ng-bind=\"\'info_outline\'\" ng-if=editandoUA()><md-tooltip>Dados atualizados nas demais UAs</md-tooltip></md-icon></label><input campo-ajuda=INC_FORM_LOC_BAIRRO ng-model=imovel.enderecamento.bairro ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" name=bairro ng-disabled=\"isBairroDisabled() || desabilitar\" ng-class=\"isDadoAlterado(\'imovel.enderecamento.bairro\')\"></md-input-container></div><div layout-gt-sm=row class=tamanho-container ng-show=!isPaisBrasil()><md-input-container class=md-block flex-gt-sm ng-show=\"cidadesExteriores.length > 0\"><label>Cidade Exterior</label><md-select campo-ajuda=INC_FORM_LOC_CIDADE_EXTERIOR ng-model=cidadeExterior name=cidadeExterior ng-required=\"!isPaisBrasil() && !isImovelEspelhoDagua() && cidadesExteriores.length > 0\" ng-change=atualizaCidadeExterior(cidadeExterior) ng-disabled=\"isCidadeExteriorDisabled() || desabilitar\" ng-class=\"isDadoAlterado(\'imovel.enderecamento.cidadeExterior\')\"><md-option ng-repeat=\"cidade in cidadesExteriores\" value={{cidade}}>{{cidade}}</md-option><md-option value=Outro>Outro</md-option></md-select></md-input-container><md-input-container class=\"md-block padding-padrao-interface margem-top-padrao margem-baixa-padrao\" flex-gt-sm ng-show=\"cidadeExterior == \'Outro\' || cidadesExteriores.length == 0\"><label>Cidade Exterior</label><input campo-ajuda=INC_FORM_LOC_CIDADE_EXTERIOR name=cidadeExteriorText ng-model=imovel.enderecamento.cidadeExterior ng-required=\"!isPaisBrasil() && !isImovelEspelhoDagua() && (imovel.enderecamento.cidadeExterior == \'Outro\' || cidadesExteriores.length == 0)\" ng-disabled=\"isCidadeExteriorDisabled() || desabilitar\" ng-class=\"isDadoAlterado(\'imovel.enderecamento.cidadeExterior\')\"></md-input-container><md-input-container class=\"md-block margem-top-padrao margem-baixa-padrao\" flex-gt-xs><label>Código Postal</label><input campo-ajuda=INC_FORM_LOC_CODIGO_POSTAL ng-model=imovel.enderecamento.codigoPostal ng-required=\"!isPaisBrasil() && !isImovelEspelhoDagua()\" ng-class=\"isDadoAlterado(\'imovel.enderecamento.codigoPostal\')\" ng-disabled=\"isCodigoPostalDisabled() || desabilitar\"></md-input-container></div><div class=ultimo-container><md-input-container class=md-block ng-show=\"isPaisBrasil() && imovel.enderecamento.cep\"><label>Complemento</label><textarea campo-ajuda=INC_FORM_LOC_COMPLEMENTO ng-model=imovel.complemento md-maxlength=500 ng-class=\"isDadoAlterado(\'imovel.complemento\')\" ng-disabled=\"isComplementoDisabled() || desabilitar\"></textarea></md-input-container><md-input-container class=\"md-block margem-top-padrao margem-baixa-padrao\" ng-show=!isPaisBrasil()><label>Endereço</label><textarea campo-ajuda=INC_FORM_LOC_ENDERECO ng-model=imovel.enderecamento.endereco md-maxlength=500 ng-class=\"isDadoAlterado(\'imovel.enderecamento.endereco\')\" ng-disabled=\"isEnderecoDisabled() || desabilitar\" ng-required=!isPaisBrasil()>\n                                                  </textarea></md-input-container></div></div></v-pane-content></v-pane></v-accordion></div></div></div></v-pane-content></v-pane></v-accordion></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/proprietario/template/dados-proprietario.html","<div ng-controller=ConsultarCpfController><div><h4>Proprietário</h4><div layout=row layout-xs=column><md-input-container flex><label>Tipo de Proprietário</label><md-select campo-ajuda=INC_FORM_PROPRIETARIO_TIPO_PROP ng-model=imovel.proprietario.tipoProprietarioReal ng-disabled=\"isFracionamento()||\n                       isFusao() ||\n                       isOriginalmenteDaUniao() ||\n                       isImovelDeTerceiros() ||\n                       isDetail || desabilitar\" ng-change=\"isParcialmente(); verificaUgProprietarioReal(); setUniaoCnpj(); imovel.registradoCartorio = false; verificarEAplicarUg()\" ng-model-options=\"{trackBy: \'$value.id\'}\" required name=tipoProprietarioReal ng-required=true><md-option campo-ajuda={{proprietario.codigoSistemico}} ng-show=!naoExibeTerceirosProprietarioReal(proprietario) ng-repeat=\"proprietario in tiposProprietarioReal| orderByBr:\'descricao\'\" ng-value=proprietario>{{proprietario.descricao}}</md-option></md-select></md-input-container><md-input-container ng-if=\"imovel.proprietario.tipoProprietarioReal.codigoSistemico === \'INC_FORM_PROPRIETARIO_UNIAO_PROP\'\" flex><label>CNPJ da União</label><input ng-disabled=\"imovel.proprietario.tipoProprietarioReal.codigoSistemico === \'INC_FORM_PROPRIETARIO_UNIAO_PROP\' || isDetail || desabilitar\" type=text ng-model=imovel.cnpjProprietario maxlength=18 class=secaoDadosIncorporacao name=cnpjproprietario></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs><label>UG Responsável</label><input type=text ng-model=imovel.proprietario.ugresponsavel ng-disabled=true class=secaoDetalhesImovel><div class=hint style=\"font-size: 12px; color: #757575; margin-top: 4px\">Campo preenchido automaticamente pelo sistema</div></md-input-container><md-input-container class=md-block flex-gt-xs><label>UG Síndica</label><!-- Campo hidden para armazenar o código --> <input type=hidden ng-model=imovel.proprietario.codigoUgSindica><!-- Visualização: campo read-only --> <input type=text ng-model=ugSindicaDisplay ng-disabled=true ng-if=isDetail class=secaoDetalhesImovel><!-- Edição: md-autocomplete --><md-autocomplete ng-if=!isDetail md-clear-button=true name=ugSindicaAutocomplete md-selected-item-change=onUgSindicaChange(ug) md-selected-item=imovel.proprietario.ugSindicaObj md-search-text=ugSindicaSearch md-items=\"ug in listaUgs | filter:ugSindicaSearch\" md-item-text=ug.display md-min-length=0 ng-required=true placeholder=\"Digite para buscar ou selecione\"><md-item-template><span>{{ug.codigo}} - {{ug.nome}}</span></md-item-template><md-not-found>Nenhuma UG encontrada para \"{{ugSindicaSearch}}\"</md-not-found></md-autocomplete><div ng-messages=forms.proprietarioForm.ugSindicaAutocomplete.$error ng-if=!isDetail><div ng-message=required>Campo obrigatório</div></div></md-input-container></div></div><div ng-if=\"imovel.proprietario.tipoProprietarioReal.descricao === \'Terceiros\'\"><fieldset class=fieldset><legend><label style=\"color: #4caf50\">Terceiros</label></legend><div><ng-form isolate-form ng-if=!desabilitar><div layout-gt-xs=row class=layout-gt-xs-row><md-input-container flex-gt-xs><label>CPF/CNPJ</label><input campo-ajuda=INC_FORM_DADOS_GERAIS_MATRIC_TRANSC ng-disabled=\"isDetail || desabilitar\" type=text ng-required=\"isImovelDeTerceiros() && imovel.proprietariosTerceiros.length === 0\" ng-model=terceiros.proprietarios.cpf ng-blur=\"validarCpfCnpj(terceiros.proprietarios.cpf, \'terceiros\');\" ng-change=\"mascaraCpfCnpj(terceiros.proprietarios.cpf, \'terceiros\'); limparNomeProprietario(\'terceiros\');\" maxlength=18 class=secaoDadosIncorporacao name=numeroMatricula></md-input-container><div style=\"margin-left: 15px\" layout=column flex><div style=\"margin: 8px 0 7px 0; font-size: 13px\">Nome/Razão Social</div><div campo-ajuda=INC_FORM_TERRENO_AREA_GEOM_TERREN_CART><b>{{terceiros.proprietarios.nome}}</b></div></div></div><div layout=row layout-align=\"end center\"><md-button class=\"md-primary md-raised primeira-maiuscula\" ng-click=\"adicionaCpfCnpj(terceiros.proprietarios, \'terceiros\')\" ng-disabled=\"!terceiros.isCpfValido || desabilitar\">Adicionar</md-button></div></ng-form></div><!-- Tabela Template --><div><md-table-container><table md-table><thead md-head md-order=ordenacao><tr md-row><th md-column>CPF/CNPJ</th><th md-column>Nome/Razão Social</th><th md-column>Acões</th></tr></thead><tbody md-body><tr md-row ng-show=\"imovel.proprietariosTerceiros.length === 0\"><td md-cell colspan=6>Nenhum Registro</td></tr><tr md-row ng-repeat=\"n in imovel.proprietariosTerceiros  | orderBy:\'-id\'\"><td md-cell>{{ n.cpf }}</td><td md-cell>{{ n.nome }}</td><td md-cell><md-button style=\"padding: 0\" class=\"md-fab md-raised md-mini\" aria-label=\"Remover Matrícula\" ng-click=\"excluirPessoa(n.cpf, \'terceiros\')\" ng-show=\"!isDetail && !desabilitar\"><md-icon style=\"color: #888\" ng-bind=\"\'delete\'\"><md-tooltip>Remover</md-tooltip></md-icon></md-button></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacaoCpfCnpj.limit md-limit-options=paginacaoCpfCnpj.limitOptions md-page=paginacaoCpfCnpj.page md-total=\"{{ imovel.proprietariosTerceiros.length }}\" md-page-select ng-disabled=\"imovel.proprietariosTerceiros.length < 6\"></md-table-pagination></div></fieldset><div layout=column layout-xs=column ng-if=isLocacao()><fieldset class=fieldset style=\"display: flex;flex-direction: column\"><legend><label style=\"color: #4caf50\">Locatário</label></legend><md-input-container><label>Órgão Locatário</label><md-select campo-ajuda=INC_FORM_PROPRIETARIO_TIPO_PROP ng-model=imovel.proprietario.tipoProprietarioLocatario ng-disabled=\"isDetail || desabilitar\" ng-change=setUniaoCnpjLocatario(); ng-model-options=\"{trackBy: \'$value.id\'}\" required name=tipoProprietarioReal ng-required=true><md-option campo-ajuda={{proprietario.codigoSistemico}} ng-show=\"proprietario.codigoSistemico != \'INC_FORM_PROPRIETARIO_TERC_PROP\'\" ng-repeat=\"proprietario in tiposProprietarioReal| orderByBr:\'descricao\'\" ng-value=proprietario>{{proprietario.descricao}}</md-option></md-select></md-input-container><md-input-container ng-if=\"imovel.proprietario.tipoProprietarioLocatario.codigoSistemico === \'INC_FORM_PROPRIETARIO_UNIAO_PROP\'\"><label>CNPJ da União</label><input ng-disabled=\"true || desabilitar\" type=text ng-model=imovel.proprietario.cnpjLocatario maxlength=18 class=secaoDadosIncorporacao name=\"cnpjLocatario \"></md-input-container><div ng-if=\"imovel.proprietario.tipoProprietarioLocatario.codigoSistemico === \'INC_FORM_PROPRIETARIO_AUTARQ_PROP\'\"><dados-autarquia proprietario=imovel.proprietario is-detail=isDetail desabilitar=desabilitar is-locatario=true></dados-autarquia></div></fieldset></div></div><md-input-container ng-show=\"imovel.proprietario.tipoProprietarioReal.codigoSistemico === \'INC_FORM_PROPRIETARIO_UNIAO_PROP\'\"><md-checkbox ng-model=imovel.registradoCartorio ng-change=\"limparTabela(\'cartorio\')\" ng-disabled=\"isDetail || desabilitar\">Possui outro proprietário registrado no cartório</md-checkbox></md-input-container><div ng-show=imovel.registradoCartorio><fieldset class=fieldset><legend><label style=\"color: rgb(175,14,172)\">Proprietário Registrado no Cartório</label></legend><div><ng-form isolate-form><div layout-gt-xs=row class=layout-gt-xs-row><md-input-container flex-gt-xs><label>CPF/CNPJ</label><input campo-ajuda=INC_FORM_DADOS_GERAIS_MATRIC_TRANSC required ng-disabled=\"isDetail || desabilitar\" type=text ng-model=cartorio.proprietarios.cpf ng-blur=\"validarCpfCnpj(cartorio.proprietarios.cpf, \'cartorio\')\" ng-change=\"mascaraCpfCnpj(cartorio.proprietarios.cpf, \'cartorio\'); limparNomeProprietario(\'cartorio\')\" maxlength=18 class=secaoDadosIncorporacao name=numeroMatricula></md-input-container><div style=\"margin-left: 15px\" layout=column flex><div style=\"margin: 8px 0 7px 0; font-size: 13px\">Nome/Razão Social</div><div campo-ajuda=INC_FORM_TERRENO_AREA_GEOM_TERREN_CART><b>{{cartorio.proprietarios.nome}}</b> <b>{{nomeEmpresarial}}</b></div></div></div><div layout=row layout-align=\"end center\"><md-button class=\"md-primary md-raised primeira-maiuscula\" ng-click=\"adicionaCpfCnpj(cartorio.proprietarios, \'cartorio\')\" ng-disabled=\"!cartorio.isCpfValido || desabilitar\">Adicionar</md-button></div></ng-form></div><!-- Tabela Template --><div><md-table-container><table md-table><thead md-head md-order=ordenacao><tr md-row><th md-column>CPF/CNPJ</th><th md-column>Nome/Razão Social</th><th md-column>Acões</th></tr></thead><tbody md-body><tr md-row ng-show=\"imovel.proprietariosCartorio.length === 0\"><td md-cell colspan=6>Nenhum Registro</td></tr><tr md-row ng-repeat=\"n in imovel.proprietariosCartorio\"><td md-cell>{{ n.cpf }}</td><td md-cell>{{ n.nome }}</td><td md-cell><md-button style=\"padding: 0\" class=\"md-fab md-raised md-mini\" aria-label=\"Remover Matrícula\" ng-click=\"excluirPessoa(n.cpf, \'cartorio\')\" ng-show=\"!isDetail && !desabilitar\"><md-icon style=\"color: #888\" ng-bind=\"\'delete\'\"><md-tooltip>Remover</md-tooltip></md-icon></md-button></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacaoCpfCnpj.limit md-limit-options=paginacaoCpfCnpj.limitOptions md-page=paginacaoCpfCnpj.page md-total=\"{{ imovel.proprietariosCartorio.length }}\" md-page-select ng-disabled=\"imovel.proprietariosCartorio.length < 6\"></md-table-pagination></div></fieldset></div><dados-autarquia ng-if=isTipoProprietarioAutarquiaFundacao() proprietario=imovel.proprietario desabilitar=desabilitar is-detail=isDetail is-locatario=false imovel=imovel></dados-autarquia></div><br><div layout=row layout-xs=column ng-if=isTipoProprietarioParcialAutarquiaFundacao()><md-input-container class=margem-direita-padrao ng-class=\"forms.proprietarioForm.tipoProprietarioParcial.$error.required && submittedProprietario ?\'md-input-invalid\': null\" flex-xs=100 flex=50><label>Órgão/Entidade</label><md-select campo-ajuda=INC_FORM_PROPRIETARIO_ORG_ENT_PARC class=secaoDadosProprietario ng-model=ctrl.orgaoEntidadeProprietarioParcial ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"isFracionamento() || isFusao() || isDetail || desabilitar\" name=tipoProprietarioParcial ng-change=buscaUgsProprietarioParcial() required><md-option ng-repeat=\"item in orgaosEntidades\" ng-value=item>{{item.nome}}</md-option></md-select></md-input-container></div><div ng-if=isAquisicao()><h4>Proprietário Anterior</h4><div layout=column layout-xs=column><md-input-container ng-if=!isAquisicaoPorSucessaoPorEntidadesdaAPF() flex-xs=100 flex=33><label>Proprietário</label><md-select campo-ajuda=INC_FORM_PROPRIETARIO_ORG_ENT_ANT class=secaoDadosProprietario ng-model=imovel.proprietarioAnterior.tipoProprietarioReal ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"isFracionamento() || isDetail || desabilitar\" ng-change=limpaTabelaTerceiros() name=tipoProprietario ng-required=isAquisicao()><md-option campo-ajuda={{item.codigoSistemico}} ng-show=!naoExibeUniaoProprietarioAnterior(item) ng-repeat=\"item in tiposProprietarioAnterior| orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><dados-autarquia ng-if=isTipoProprietarioAnteriorAutarquiaFundacao() proprietario=imovel.proprietarioAnterior desabilitar=desabilitar is-detail=isDetail is-locatario=false imovel=imovel></dados-autarquia></div><md-input-container ng-if=\"isAquisicaoPorSucessaoPorEntidadesdaAPF() && !exibirNovaEntidade\" ng-class=\"forms.proprietarioForm.entiExtinta.$error.required && submittedProprietario? \'md-input-invalid\' : null\" class=md-block flex><label>Órgão/Entidade</label><md-autocomplete class=secaoDadosProprietario ng-required=isAquisicaoPorSucessaoPorEntidadesdaAPF() md-input-name=entiExtinta md-no-cache=true ng-disabled=\"isFracionamento() || isDetail || desabilitar\" md-items=\"item in querySearch(searchText)\" md-selected-item=imovel.proprietarioAnterior.entidadeExtinta md-search-text=searchText md-min-length=0 md-selected-item-change=selectedItemChange(item) required md-item-text=item.display><md-item-template><span md-highlight-text=searchText>{{item.display}}</span></md-item-template></md-autocomplete><div layout=row layout-align=\"end center\" ng-if=\"!isDetail && !desabilitar\"><md-button ng-show=isAquisicaoPorSucessaoPorEntidadesdaAPF() ng-click=exibirFormularioEntidade() md-no-ink class=\"md-raised md-primary\">Nova Entidade Extinta</md-button></div></md-input-container><div ng-if=exibirNovaEntidade><fieldset class=fieldset><legend><label>Incluir Nova Entidade Extinta</label></legend><div><div layout=row><md-input-container flex><label>Descrição *</label><input type=text ng-model=novaEntidade.descricao></md-input-container></div><div layout=row><div layout=column><md-input-container flex><label>Ordem</label><input type=text ng-model=novaEntidade.ordem></md-input-container></div><div layout=column><md-input-container flex><label>Sigla</label><input type=text ng-model=novaEntidade.sigla></md-input-container></div></div><div layout=row><md-input-container flex><label>Fundamento Legal</label><input type=text ng-model=novaEntidade.fundamento></md-input-container></div><div layout=row layout-align=\"end center\"><md-button class=\"md-primary md-raised primeira-maiuscula\" ng-click=incluirEntidade()>Adicionar</md-button></div></div></fieldset></div><!-- Terceiros --><field-set-terceiros imovel=imovel is-detail=isDetail proprietario=imovel.proprietarioAnterior tipo-proprietario=anterior desabilitar=desabilitar></field-set-terceiros></div><div layout=row ng-if=\"isDetail && imovel.motivoAlteracao\"><div layout=column flex><md-input-container class=md-block><div layout=row class=subtitulo-padrao>Motivo da Alteração</div><br><textarea ng-model=imovel.motivoAlteracao class=overflow-textarea disabled aria-label=\"Motivo alteração\"></textarea></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/restricoes-uso-ocupacao/templates/restricoes-uso-ocupacao.html","<div layout=column><md-input-container class=margem-pequena ng-show=isEspelhoOuCavidade()><md-checkbox campo-ajuda=INC_FORM_TERRENO_ZEIS class=secaoRestricoesUsoOcupacao ng-model=terreno.isAreaInteresseSocial ng-disabled=\"isDetail || desabilitar\" ng-click=exibirMsgConfirmacaoEdicao();>Zona/Área de Interesse Social(ZEIS)</md-checkbox></md-input-container><md-input-container class=margem-pequena><md-checkbox campo-ajuda=INC_FORM_TERRENO_SITUADO_AREA_SEG class=secaoRestricoesUsoOcupacao ng-model=terreno.isAreaSeguranca ng-disabled=\"isDetail || desabilitar\" ng-click=exibirMsgConfirmacaoEdicao();>Situado em área de segurança</md-checkbox></md-input-container><md-input-container class=margem-pequena ng-show=isEspelhoOuCavidade()><md-checkbox campo-ajuda=INC_FORM_TERRENO_AREA_NON_AED class=secaoRestricoesUsoOcupacao ng-model=terreno.isAreaNonAedificandi ng-disabled=\"isDetail || desabilitar\" ng-click=exibirMsgConfirmacaoEdicao();>Área Non Aedificandi</md-checkbox></md-input-container><div ng-show=isEspelhoOuCavidade() layout=column><md-input-container class=margem-pequena><md-checkbox campo-ajuda=INC_FORM_TERRENO_TOMBADO_PATRIMONIO class=secaoRestricoesUsoOcupacao ng-model=terreno.isPatrimonioTombado ng-disabled=\"isDetail || desabilitar\" ng-click=exibirMsgConfirmacaoEdicao();>Tombado como patrimônio histórico, artístico e/ou cultural</md-checkbox></md-input-container><md-input-container ng-class=\"forms.terrenoForm.fracaoIdealImovelCadastral.$error.required && submittedTerreno? \'md-input-invalid\': null\" class=no-margin-top ng-show=terreno.isPatrimonioTombado><md-select class=secaoRestricoesUsoOcupacao ng-model=terreno.descricaoPatrimonioTombado aria-label=\"Terreno com Acenssão/Benfeitoria?\" placeholder=\"Selecione uma opção\" ng-disabled=\"!terreno.isPatrimonioTombado || isDetail || desabilitar\" ng-required=terreno.isPatrimonioTombado ng-click=exibirMsgConfirmacaoEdicao();><md-option campo-ajuda=INC_FORM_TERRENO_FEDERAL value=Federal>Federal</md-option><md-option campo-ajuda=INC_FORM_TERRENO_ESTADUAL value=Estadual>Estadual</md-option><md-option campo-ajuda=INC_FORM_TERRENO_MUNICIPAL value=Municipal>Municipal</md-option></md-select></md-input-container></div><div layout=column><md-input-container class=margem-pequena><md-checkbox campo-ajuda=INC_FORM_TERRENO_OUTRA_RESTRICAO class=secaoRestricoesUsoOcupacao ng-model=terreno.isOutraRestricao ng-disabled=\"isDetail || desabilitar\" ng-click=exibirMsgConfirmacaoEdicao();>Outra restrição de uso/ocupação</md-checkbox></md-input-container><md-input-container ng-class=\"forms.terrenoForm.descricaoOutraRestricao.$error.required && submittedTerreno?\'md-input-invalid md-input-focused\':null\" class=no-margin-top ng-show=terreno.isOutraRestricao><textarea campo-ajuda=INC_FORM_TERRENO_DESC_RESTRICAO class=secaoRestricoesUsoOcupacao ng-model=terreno.descricaoOutraRestricao md-maxlength=500 placeholder=\"Descrição da Restrição\" ng-required=terreno.isOutraRestricao ng-model=terreno.descricaoOutraRestricao ng-disabled=\"!terreno.isOutraRestricao || isDetail || desabilitar\" name=descricaoOutraRestricao ng-click=exibirMsgConfirmacaoEdicao();>\n            </textarea></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/unidade-autonoma/templates/dados-edificacao-unidade-autonoma.html","<div layout=column><div layout=row ng-show=\"!isdetalhamento() && !desabilitar\"><div campo-ajuda=INC_FORM_UN_AUTONOMA_SELEC_OPCAO ng-required=true>Selecione uma opção</div><md-radio-group ng-class=\"(!tipoCadastro  && $parent.submittedUnidadeAutonoma) || (!tipoCadastro  && submittedUnidadeAutonoma) ? \'radio-invalid md-off\' : null\" ng-model=tipoCadastro layout=row ng-required=exibirCamposSelecaoTipoCadastro() style=\"margin: 15px\" name=tipoCadastroEdificacao ng-click=\"alterarTipoCadastro(); exibirMsgConfirmacaoEdicao();\"><md-radio-button campo-ajuda=INC_FORM_UN_AUTONOMA_EDIF_CADASTRADA value=EXISTENTE flex>Selecionar uma edificação cadastrada</md-radio-button><md-radio-button campo-ajuda=INC_FORM_UN_AUTONOMA_NOVA_EDIFIC ng-click=habilitarDesenho(2) value=NOVA flex>Cadastrar nova edificação</md-radio-button></md-radio-group><div layout=row layout-align=\"end center\" ng-show=exibeNovaUA()><div layout=column style=\"margin: 15px\"><md-radio-group ng-class=\"(!unidadeAutonoma.edificacao.tipoGeometria  && $parent.submittedUnidadeAutonoma) ? \'radio-invalid md-off\' : null\" layout=row layout-align=\"start start\" ng-change=alterarTipoDesenho() ng-model=unidadeAutonoma.edificacao.tipoGeometria><div layout=column><md-radio-button campo-ajuda=INC_FORM_UN_AUTONOMA_ARQUIVO ng-disabled=dadosEdificacaoDisabled() value=ARQUIVO>Arquivo (Shapefile)</md-radio-button><ci-input-file model=shapefileUnidadeAutonoma objeto=unidadeAutonoma.edificacao.arquivoGeometria desabilitado=\"unidadeAutonoma.edificacao.tipoGeometria != \'ARQUIVO\' || isdetalhamento() || desabilitar\" botao=exibirBotao cif-onchange=uploadShapefile() cif-onremove=removerShapefile()></ci-input-file></div><div layout=column><md-radio-button campo-ajuda=INC_FORM_UN_AUTONOMA_VETORIZACAO ng-disabled=dadosEdificacaoDisabled() value=VETORIZACAO flex>Vetorização</md-radio-button></div></md-radio-group></div></div></div><div><div layout=row><md-input-container style=\"height: 50px\" flex><label>Tipo de Edificação</label><md-select campo-ajuda=INC_FORM_UN_AUTONOMA_TIPO_EDIFIC ng-disabled=dadosEdificacaoDisabled() ng-model=unidadeAutonoma.edificacao.tipoEdificacao ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoEdificacaoUnidadeAutonoma ng-required=true ng-click=exibirMsgConfirmacaoEdicao();><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposEdificacao| orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex ng-show=exibirCampoPossuiElevador()><md-checkbox campo-ajuda=INC_FORM_UN_AUTONOMA_POSSUI_ELEVAD ng-model=unidadeAutonoma.edificacao.possuiElevador ng-disabled=dadosEdificacaoDisabled() name=possuiElevador ng-click=exibirMsgConfirmacaoEdicao();>Possui Elevador</md-checkbox></md-input-container></div><div layout=row><md-input-container flex class=margem-direita-padrao><label>Tipo de Projeto</label><md-select campo-ajuda=INC_FORM_UN_AUTONOMA_TIPO_PROJETO ng-disabled=dadosEdificacaoDisabled() ng-model=unidadeAutonoma.edificacao.tipoProjeto name=tipoProjeto ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" ng-click=exibirMsgConfirmacaoEdicao();><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposProjeto| orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex><label>Nome da Edificação</label><input ng-disabled=dadosEdificacaoDisabled() type=text ng-model=unidadeAutonoma.edificacao.nome name=nomeEdificacao ng-required=true maxlength=150 md-maxlength=150 ng-click=exibirMsgConfirmacaoEdicao();></md-input-container></div><div layout=row><md-input-container flex><label>Observações Gerais da Edificação</label><textarea campo-ajuda=INC_FORM_UN_AUTONOMA_OBS_GERAIS_ED ng-disabled=dadosEdificacaoDisabled() ng-model=unidadeAutonoma.edificacao.observacoes name=observacoesEdificacao maxlength=2000 md-maxlength=2000 ng-click=exibirMsgConfirmacaoEdicao();></textarea></md-input-container></div><v-accordion multiple class=vAccordion--default style=\"padding-left: 20px\"><v-pane ng-if=!imovelCtrl.isGaragem><v-pane-header ng-click=\"imovelCtrl.validaSecao($event, forms.adicionarUnidadeAutonomaForm, 3, \'.secaoDadosCondicoesAcessibilidade\')\">Condições de Acessibilidade</v-pane-header><v-pane-content><condicoes-acessibilidade action={{getAcaoAcessibilidade()}} model=unidadeAutonoma.condicoesAcessibilidade desabilitar=desabilitar possui-elevador=unidadeAutonoma.edificacao.possuiElevador is-garagem=false></condicoes-acessibilidade></v-pane-content></v-pane><v-pane ng-if=!imovelCtrl.isGaragem><v-pane-header ng-click=\"imovelCtrl.validaSecao($event, forms.adicionarUnidadeAutonomaForm, 3, \'.secaoDadosMedidor\')\">Medidor</v-pane-header><v-pane-content><dados-medidor action={{action}} uf=enderecamento.uf model=unidadeAutonoma.edificacao.dadosMedidor desabilitar=desabilitar></dados-medidor></v-pane-content></v-pane><v-pane ng-if=!imovelCtrl.isGaragem><v-pane-header ng-click=\"imovelCtrl.validaSecao($event, forms.adicionarUnidadeAutonomaForm, 3, \'.secaoDadosDocumentacao\')\">Documentação</v-pane-header><v-pane-content><dados-documentacao action=unidadeAutonoma model=unidadeAutonoma.edificacao.documentacao show-tab-form-documento=showTabFormDocumento show-tab-inclusao-benfeitoria=false documento=documento documento-backup=documentoBackup documentos-inc=documentos detail=\"action==\'detail\'\" desabilitar=desabilitar></dados-documentacao></v-pane-content></v-pane></v-accordion></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/unidade-autonoma/templates/dados-garagem-unidade-autonoma.html","<div layout=column><div layout=row><md-input-container flex><label>Área (m²)</label><input campo-ajuda=INC_FORM_UN_AUTONOMA_AREA_GARAG type=text ui-number-mask=2 ng-model=unidadeAutonoma.area name=areaGaragem ng-required=true ng-disabled=dadosGaragemDisabled()></md-input-container><md-input-container flex ng-class=\"forms.terrenoForm.fracaoIdealImovelCadastral.$error.required && submittedTerreno?\'md-input-invalid\':null\"><label>Fração Ideal do Imóvel</label><input campo-ajuda=INC_FORM_TERRENO_FRACAO_IDEAL_IM_CAD type=text name=fracaoIdealCadastral ng-model=unidadeAutonoma.fracaoIdealCadastral ng-disabled=dadosGaragemDisabled() ng-required=true ng-change=formatarNumero(unidadeAutonoma.fracaoIdealCadastral);></md-input-container><md-input-container flex><label>Tipo de Garagem</label><md-select campo-ajuda=INC_FORM_UN_AUTONOMA_TIPO_GARAG ng-model=unidadeAutonoma.tipoGaragem.id name=tipoGaragem ng-required=true ng-disabled=dadosGaragemDisabled()><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposGaragem| orderByBr:\'descricao\'\" ng-value=item.id>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row><md-input-container flex><label>Memorial Descritivo</label><textarea campo-ajuda=INC_FORM_UN_AUTONOMA_MEMOR_DESC_GARAG class=overflow-textarea ng-model=unidadeAutonoma.memorialDescritivo name=memorialDescritivoGaragem ng-disabled=dadosGaragemDisabled()></textarea></md-input-container></div><condicoes-acessibilidade model=unidadeAutonoma.condicoesAcessibilidade is-garagem=true action={{action}} desabilitar=desabilitar></condicoes-acessibilidade><upload-fotos model=unidadeAutonoma.fotos action={{action}} desabilitar=desabilitar></upload-fotos><upload-arquivos model=unidadeAutonoma.plantas action={{action}} titulo=\"Arquivos de Planta\" desabilitar=desabilitar></upload-arquivos></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/unidade-autonoma/templates/dados-unidade-autonoma.html","<div layout=column><div layout=row flex><md-input-container flex><label>Área construída</label><input campo-ajuda=INC_FORM_UN_AUTONOMA_AREA_CONSTR ng-disabled=dadosUnidadeAutonomaDisabled() ng-model=unidadeAutonoma.area required ui-number-mask name=areaConstruida></md-input-container><md-input-container ng-if=showNumeroDormitorios() flex class=md-block><label>Número de dormitórios</label><input campo-ajuda=INC_FORM_UN_AUTONOMA_N_DORM ng-disabled=dadosUnidadeAutonomaDisabled() ng-model=unidadeAutonoma.numeroDormitorios type=text ui-number-mask=0 name=numeroDormitorios></md-input-container></div><div layout=row flex><md-input-container flex ng-class=\"forms.terrenoForm.fracaoIdealImovelCadastral.$error.required && submittedTerreno?\'md-input-invalid\':null\"><label>Fração Ideal do Imóvel</label><input campo-ajuda=INC_FORM_TERRENO_FRACAO_IDEAL_IM_CAD type=text name=fracaoIdealImovel class=secaoDescricaoCadastral ng-model=imovel.fracaoIdealImovel ng-disabled=dadosUnidadeAutonomaDisabled() ng-required=isImovelUnidadeAutonoma() ng-blur=validarFracaoIdealImovel() ng-change=\"formatarNumero(imovel.fracaoIdealImovel); calculaFracaoAreaTotal(); calculaFracaoAreaUniao()\" maxlength=11></md-input-container><md-input-container flex><div style=\"margin-left: 15px\" layout=column flex ng-if=\"imovel.terreno.areaTotalCadastral && imovel.fracaoIdealImovel != null && imovel.fracaoIdealImovel > \'0\' && imovel.fracaoIdealImovel != \'0,\'\"><div style=\"margin: 8px 0 7px 0; font-size: 13px\">Fração da Área Total do Terreno</div><div campo-ajuda=INC_FORM_TERRENO_AREA_GEOM_TERREN_CART><b>{{imovel.fracaoAreaTotal}}</b></div></div><div style=\"margin-left: 15px\" layout=column flex ng-if=\"imovel.terreno.areaUniaoCadastral && imovel.fracaoIdealImovel != null && imovel.fracaoIdealImovel != \'0\' && imovel.fracaoIdealImovel != \'0,\'\"><div style=\"margin: 8px 0 7px 0; font-size: 13px\">Fração da Área da União</div><div campo-ajuda=INC_FORM_TERRENO_AREA_GEOM_TERREN_CART><b>{{imovel.fracaoAreaUniao}}</b></div></div></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs><label>Memorial Descritivo</label><textarea campo-ajuda=INC_FORM_UN_AUTONOMA_MEMOR_DESC ng-disabled=dadosUnidadeAutonomaDisabled() class=overflow-textarea ng-model=unidadeAutonoma.memorialDescritivo name=memorialDescritivo md-maxlength=2000></textarea></md-input-container></div><div layout=column><div campo-ajuda=INC_FORM_UN_AUTONOMA_POSSUI_GARAG ng-class=\"!unidadeAutonoma.possuiGaragem && $parent.submittedUnidadeAutonoma? \'input-invalid\' : null\">Possui garagem? *</div><md-radio-group ng-class=\"!unidadeAutonoma.possuiGaragem  && $parent.submittedUnidadeAutonoma? \'radio-invalid\' : null\" ng-model=unidadeAutonoma.possuiGaragem style=\"margin: 15px\" layout=row required name=possuiGaragem><md-radio-button campo-ajuda=INC_FORM_UN_AUTONOMA_POSSUI_GARAG_SIM ng-disabled=dadosUnidadeAutonomaDisabled() value=Sim>Sim</md-radio-button><md-radio-button campo-ajuda=INC_FORM_UN_AUTONOMA_POSSUI_GARAG_NAO ng-disabled=dadosUnidadeAutonomaDisabled() value=Não>Não</md-radio-button><md-radio-button campo-ajuda=INC_FORM_UN_AUTONOMA_POSSUI_GARAG_SEM_INFO ng-disabled=dadosUnidadeAutonomaDisabled() value=\"Sem informação\">Sem informação</md-radio-button></md-radio-group></div><div layout=column ng-if=isUnidadeAutonomaComGaragem()><div campo-ajuda=INC_FORM_UN_AUTONOMA_N_VAGAS style=\"padding-top: 40px\">Nº de vagas</div><div layout=row><md-input-container flex><label>Cobertas</label><input campo-ajuda=INC_FORM_UN_AUTONOMA_COBERTAS ng-disabled=dadosUnidadeAutonomaDisabled() type=text ui-number-mask=0 ng-required=isUnidadeAutonomaComGaragem() ng-model=unidadeAutonoma.numeroVagasCobertas name=numeroVagasCobertas></md-input-container><md-input-container flex><label>Descobertas</label><input campo-ajuda=INC_FORM_UN_AUTONOMA_DESCOBERTAS ng-disabled=dadosUnidadeAutonomaDisabled() type=text ui-number-mask=0 ng-required=isUnidadeAutonomaComGaragem() ng-model=unidadeAutonoma.numeroVagasDescobertas name=numeroVagasDescobertas></md-input-container><div flex layout=row layout-align=\"center center\">Total = {{unidadeAutonoma.numeroVagasCobertas + unidadeAutonoma.numeroVagasDescobertas || 0}}</div></div></div><div layout=row><md-input-container flex><label>Observações Gerais da Unidade Autônoma</label><textarea campo-ajuda=INC_FORM_UN_AUTONOMA_OBS_GERAIS_UN_AUT ng-disabled=dadosUnidadeAutonomaDisabled() ng-model=unidadeAutonoma.observacoes name=observacoes maxlength=2000 md-maxlength=2000></textarea></md-input-container></div><upload-fotos action={{action}} model=unidadeAutonoma.fotos desabilitar=desabilitar></upload-fotos><upload-arquivos action={{action}} model=unidadeAutonoma.plantas titulo=\"Arquivos de Planta\" desabilitar=desabilitar></upload-arquivos></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/unidade-autonoma/templates/selecao-edificacao-unidade-autonoma.html","<div layout=column style=\"padding: 15px\" flex><div>Selecione a edificação em que a Unidade Autônoma se encontra:</div><div layout=row layout-align=\"end center\"><md-button class=\"md-raised md-primary\" ng-click=\"editarVoltar(); onVoltar();\">Voltar</md-button><md-button class=\"md-raised md-primary\" ng-click=confirmarSelecao();>Confirmar a seleção</md-button></div></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/upload-arquivos/template/upload-arquivos.html","<fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">{{titulo}}</label></legend><div layout=column flex=100><ng-form isolate-form name=forms.documentosForm layout=column><div layout=row><md-input-container ng-show=!isDetalhar() flex><label>Descrição</label><input campo-ajuda=INC_FORM_UPLOAD_ARQUIVOS_DESC required ng-model=documento.descricao name=nome maxlength=200></md-input-container><md-input-container ng-if=\"imovel.incorporacao.matriculas.length> 0 && !isDetalhar()\" flex><label>Cartorio</label><md-select campo-ajuda=INC_FORM_UPLOAD_ARQUIVOS_CARTORIO required ng-model=documento.cartorio><md-option ng-value=cartorio.id ng-repeat=\"cartorio in cartorios\">{{cartorio.nome}}</md-option></md-select></md-input-container><md-input-container ng-show=!isDetalhar() flex=50><label>Data</label><md-datepicker ng-mask=39/19/9999 restrict=reject flex=50 campo-ajuda=INC_FORM_UPLOAD_ARQUIVOS_DT_DOC required ng-model=documento.data md-max-date=ctrl.today name=data></md-datepicker></md-input-container></div><div layout-gt-xs=row><div layout=row flex><ci-input-file required show-input=mostrarCampoArquivo ng-show=!isDetalhar() model=ctrl.file></ci-input-file></div><div layout=row layout-align=\"end end\"><div style=\"margin-right: 15px\"><md-button ng-show=!isDetalhar() ng-click=adicionarDocumento() md-no-ink class=\"md-raised md-primary primeira-maiuscula\">Adicionar</md-button></div></div></div></ng-form></div><div layout-align=center ng-show=\"model.length > 0 || isDetalhar()\"><md-table-container><table md-table md-progress=promise><thead md-head md-order=ordenacao><tr md-row><th md-column md-order-by=descricao>Descrição</th><th md-column ng-show=\"imovel.incorporacao.matriculas.length > 0\">Cartório</th><th md-column md-order-by=data>Data</th><th md-column md-order-by=arquivo.nomeOriginal>Arquivo</th><th md-column ng-show=!isDetalhar()>Ações</th></tr></thead><tbody md-body><tr md-row ng-show=\"model.length === 0\"><td md-cell colspan=5>Nenhum registro</td></tr><tr md-row ng-repeat=\"item in model | orderBy: ordenacao | limitTo: paginacao.limit: (paginacao.page - 1) * paginacao.limit\"><td md-cell>{{item.descricao}}</td><td ng-show=\"imovel.incorporacao.matriculas.length > 0\" md-cell>{{getNomeCartorio(item.cartorio)}}</td><td md-cell>{{item.data | date:\"dd/MM/yyyy\"}}</td><td md-cell><a download={{item.arquivo.nomeOriginal}} href={{item.url}} target=_blank>{{item.arquivo.nomeOriginal}}</a></td><td md-cell ng-show=!isDetalhar()><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"excluirDocumento($event, item)\" class=\"md-fab md-raised md-mini\" aria-label=\"Excluir Arquivo\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir Arquivo</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacao.limit md-limit-options=paginacao.limitOptions md-page=paginacao.page md-total=\"{{ model.length }}\" md-page-select ng-disabled=\"model.length < 6\"></md-table-pagination></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/upload-fotos/template/upload-fotos.html","<fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">Fotos</label></legend><div layout=column flex=100><ng-form isolate-form name=forms.imagemForm><div layout=row flex=100><md-input-container ng-show=!isDetalhar() flex=50><label>Descrição</label><input campo-ajuda=INC_FORM_UPLOAD_FOTOS_DESC required ng-model=foto.descricao name=descricao maxlength=50></md-input-container><md-input-container ng-show=!isDetalhar() flex=50><label>Data</label><md-datepicker ng-mask=39/19/9999 restrict=reject campo-ajuda=INC_FORM_UPLOAD_FOTOS_DT_DOC required ng-model=foto.data md-max-date=ctrl.today name=data></md-datepicker></md-input-container></div><div ng-show=!isDetalhar()><ci-input-file obrigatorio=true show-input=mostrarCampoArquivo name=arquivo model=ctrl.fotoFile accept=\".jpeg, .png, .jpg, .bmp\"></ci-input-file></div><div layout=row layout-align=\"end center\" ng-show=!isDetalhar()><div style=\"margin-right: 15px\"><md-button class=\"md-raised md-primary primeira-maiuscula\" ng-click=adicionarFoto()>Adicionar</md-button></div></div></ng-form><div layout=column ng-show=\"model.length > 0 || isDetalhar()\"><md-table-container><table md-table md-progress=promise><thead md-head md-order=ordenacao><tr md-row><th md-column></th><th md-column md-order-by=descricao>Descrição</th><th md-column md-order-by=data>Data</th><th md-column md-order-by=imagem.nomeOriginal>Arquivo</th><th md-column ng-show=!isDetalhar()>Ações</th></tr></thead><tbody md-body><tr md-row ng-show=\"model.length === 0\"><td md-cell colspan=5>Nenhum registro</td></tr><tr md-row ng-repeat=\"foto in model | orderBy: ordenacao | limitTo: paginacao.limit: (paginacao.page - 1) * paginacao.limit\"><td md-cell><img ng-src=data:image/jpg;base64,{{foto.imagem.arquivo}} ng-click=\"abrirModal($event, $index)\" style=\"width: 70px; height: 40px\"></td><td md-cell>{{foto.descricao}}</td><td md-cell>{{foto.data | date:\"dd/MM/yyyy\"}}</td><td md-cell><a ng-href=\"{{ foto.imagem.url }}\" download=\"{{ foto.imagem.nomeOriginal }}\">{{ foto.imagem.nomeOriginal }}</a></td><td md-cell ng-show=!isDetalhar()><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"excluirFoto($event, foto)\" class=\"md-fab md-raised md-mini\" aria-label=\"Excluir Arquivo\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir Arquivo</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacao.limit md-limit-options=paginacao.limitOptions md-page=paginacao.page md-total=\"{{ model.length }}\" md-page-select ng-disabled=\"model.length < 6\"></md-table-pagination></div></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-benfeitoria.tmpl.html","<div layout=row><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=2 imovel=imovel action=detail habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/editar-imovel/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout=column flex=70><div layout=row layout-align=\"end right\"><md-button style=\"background-color: rgb(76,175,80); color: #fff; float: right; margin-top: 10px\" ng-click=\"resetBenfeitoriaSelecionada(); showInserirBenfeitoriaDialog($event, null)\" ng-if=!vm.isDetail>Adicionar Acessão/Benfeitoria</md-button><md-button style=\"background-color: rgb(76,175,80); color: #fff; float: right; margin-top: 10px\" ng-click=modalBenfeitoriasCanceladas($event) ng-show=\"benfeitoriasCanceladas.length > 0\">Detalhar Acessões/Benfeitorias Canceladas</md-button></div><div layout=row layout-xs=column><md-content flex><md-table-container><table md-table style=\"margin: auto\"><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th width=20%>Benfeitoria</th><th>Detalhes</th><th width=12%>Ação</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in vm.imovel.benfeitorias | filter: {ativa : true} | orderBy: \'-id\' | limitTo: vm.paginacaoBenfeitorias.limit: (vm.paginacaoBenfeitorias.page - 1) * vm.paginacaoBenfeitorias.limit\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-show=item.ativa><td md-cell style=\"text-align: center\"><img src=data:image/jpeg;base64,{{item.imagens[0].imagem}} style=\"width: 150px\"></td><td md-cell style=\"padding: 0px\"><p><span style=\"font-weight: bold\">ID:</span> {{item.id}}</p><p><span style=\"font-weight: bold\">Código:</span> {{item.codigo}}</p><p ng-if=item.edificacao.tipoEspecializacaoEdificacao><span style=\"font-weight: bold\">Tipo de Acessão/Benfeitoria:</span> {{item.edificacao.tipoEspecializacaoEdificacao.descricao}}</p><p ng-if=item.benfeitoriaComplementar.tipoEspecializacaoComplementar><span style=\"font-weight: bold\">Tipo de Acessão/Benfeitoria:</span> {{item.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao}}</p><p ng-if=item.nome><span style=\"font-weight: bold\">Nome:</span> {{item.nome}}</p><p><span style=\"font-weight: bold\">Área Construída(m²):</span> {{item.areaConstruida | number }}</p></td><td md-cell style=\"text-align: center\"><ng-md-icon icon=search style=fill:#788489 size=22 alt=\"Editar benfeitoria\" ng-click=vm.exibirTabInclusaoBenfeitoria(item)><md-tooltip>Detalhar Benfeitoria</md-tooltip></ng-md-icon><ng-md-icon icon=close style=fill:#788489 size=22 alt=\"Excluir benfeitoria\" ng-if=!vm.isDetail ng-click=\"modalCancelarBenfeitoria($event, item.id, $index)\"><md-tooltip>Cancelar benfeitoria</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto\" md-limit=vm.paginacaoBenfeitorias.limit md-limit-options=vm.paginacaoBenfeitorias.limitOptions md-page=vm.paginacaoBenfeitorias.page md-total=\"{{ vm.totalBenfeitoriasAtivas() }}\" md-on-paginate=getDesserts md-page-select></md-table-pagination></md-content></div><div layout=row layout-align=\"end right\"><md-button class=\"md-raised md-secondary\" ng-click=selectTab(1)>Retornar</md-button><md-button class=\"md-raised md-secondary\" ui-sref=INCORPORACAO.imovelConsultar>Fechar</md-button><md-button class=\"md-raised md-primary\" ng-if=\"imovel.situacaoImovel.id == 1\" ui-sref=\"INCORPORACAO.alterarCadastro({id: imovel.id, tab: 2})\" ng-click=\"vm.verificarRestricao(vm.imovel.rip, $event)\">Editar</md-button><md-button class=\"md-primary md-raised\" ng-if=\"(vm.imovel.situacaoImovel.id != 3) && !vm.isSincSpiunet()\" ng-click=vm.showCancelarImovel($event)>Cancelar Imóvel</md-button><md-button class=\"md-raised md-primary\" ng-click=selectTab(5)>Avançar</md-button></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-dados-gerais-imovel.tmpl.html","<div layout=row><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=0 imovel=imovel action=detail habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/editar-imovel/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div column=column flex=70><md-content><ng-form name=forms.imovelForm><v-accordion class=vAccordion--default><v-pane expanded><v-pane-header ng-click=\"vm.validaSecao($event, forms.imovelForm, 0, \'.secaoDetalhesImovel\')\">Detalhes do imóvel</v-pane-header><v-pane-content><secao-detalhes-imovel imovel=imovel is-detail=vm.isDetail></secao-detalhes-imovel></v-pane-content></v-pane><v-pane><v-pane-header ng-click=\"vm.validaSecao($event, forms.imovelForm, 0, \'.secaoDadosIncorporacao\')\">Dados de Incorporação</v-pane-header><v-pane-content><secao-dados-incorporacao imovel=imovel is-exibe-tab-titulo-aquisitivo=vm.mostraTabTituloAquisitivo titulo=vm.titulo titulo-backup=vm.tituloBackup query-pagination=vm.paginationTituloAquisitivo action=\"\'detail\'\"></secao-dados-incorporacao></v-pane-content></v-pane><v-pane><v-pane-header>Classificação de Utilização</v-pane-header><v-pane-content><secao-classificacao-utilizacao imovel=imovel is-detail=true></secao-classificacao-utilizacao></v-pane-content></v-pane><v-pane><v-pane-header>Fotos do Imóvel</v-pane-header><v-pane-content><upload-fotos action=detail model=imovel.imagens></upload-fotos></v-pane-content></v-pane></v-accordion></ng-form><div layout=row layout-align=\"end center\"><md-button class=\"md-secondary md-raised\" ng-click=selectTab(-1)>Retornar</md-button><md-button class=\"md-raised md-secondary\" ui-sref=INCORPORACAO.imovelConsultar>Fechar</md-button><md-button class=\"md-raised md-primary\" ng-if=\"vm.imovel.situacaoImovel.id == 1\" ui-sref=\"INCORPORACAO.alterarCadastro({id: vm.imovel.id, tab: 0})\" ng-click=\"vm.verificarRestricao(vm.imovel.rip, $event)\">Editar</md-button><md-button class=\"md-primary md-raised\" ng-if=\"(vm.imovel.situacaoImovel.id != 3) && !vm.isSincSpiunet()\" ng-click=vm.showCancelarImovel($event)>Cancelar Imóvel</md-button><md-button class=\"md-primary md-raised\" ng-click=selectTab(1)>Avançar</md-button></div></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-historico.tmpl.html","<div layout=column><div layout=row layout-align=\"center center\" ng-show=true><div flex=100><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column rowspan=2>Data</th><th md-column rowspan=2>Usuário</th><th md-column rowspan=1 colspan=3 style=\"text-align: center !important\">Dados alterados</th></tr><tr md-row><th md-column>Campo</th><th md-column>Antes</th><th md-column>Depois</th></tr></thead><tbody md-body ng-repeat=\"historico in vm.listaHistorico\"><tr md-row><td md-cell class=shine>{{historico.dtAuditoria | date: \"dd/MM/yyyy \'às\' HH:mm:ss\"}}</td><td md-cell class=shine><div style=\"display: block\"><div>{{historico.noUsuario}}</div><div>{{historico.cpfUsuario}}</div></div></td><td md-cell class=shine colspan=3 style=\"padding: 0\"><table md-table><tbody md-body><tr md-row ng-repeat=\"detalheHistorico in historico.auditorias\"><td md-cell class=shine style=\"width: 33%\">{{ detalheHistorico.campo }}</td><td ng-if=!detalheHistorico.arquivoAntes md-cell class=shine style=\"width: 33%;white-space: pre-line\">{{ detalheHistorico.antes }}</td><td ng-if=detalheHistorico.arquivoAntes md-cell class=shine style=\"width: 33%;white-space: pre-line\"><button type=button class=btn-download ng-click=\"downloadArquivoHistorico(detalheHistorico.arquivoAntes, detalheHistorico.antes, detalheHistorico.typeFileAntes)\">{{detalheHistorico.antes}}</button></td><td ng-if=!detalheHistorico.arquivoDepois md-cell style=\"width: 33%;white-space: pre-line\">{{ detalheHistorico.depois }}</td><td ng-if=detalheHistorico.arquivoDepois md-cell class=shine style=\"width: 33%;white-space: pre-line\"><button type=button class=btn-download ng-click=\"downloadArquivoHistorico(detalheHistorico.arquivoDepois, detalheHistorico.depois, detalheHistorico.typeFileDepois)\">{{detalheHistorico.depois}}</button></td></tr></tbody></table></td></tr></tbody></table></md-table-container></div></div><div layout=row layout-align=\"end center\"><md-button class=\"md-secondary md-raised\" ng-click=selectTab(5)>RETORNAR</md-button></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-localizacao-imovel.tmpl.html","<div layout-xs=column ng-controller=\"LocalizacaoController as ctrl\"><md-content flex><ng-form name=forms.localizacaoForm><inc-localizacao-imovel imovel=vm.imovel action=\"\'detail\'\"></inc-localizacao-imovel><div layout=row layout-align=\"end center\"><md-button class=\"md-raised md-secondary\" ng-if=\"vm.imovel.situacaoImovel.id === 2 && vm.imovel.situacaoCadastro.id === 3\" ng-click=selectTab(-2)>Retornar</md-button><md-button class=\"md-raised md-secondary\" ui-sref=INCORPORACAO.imovelConsultar>Fechar</md-button><md-button class=\"md-raised md-primary\" ng-if=\"vm.imovel.situacaoImovel.id == 1 || vm.imovel.situacaoCadastro.id == 3\" ui-sref=\"INCORPORACAO.alterarCadastro({id: vm.imovel.id, tab: -1})\" ng-click=\"vm.verificarRestricao(vm.imovel.rip, $event)\">Editar</md-button><md-button class=\"md-primary md-raised\" ng-if=\"(vm.imovel.situacaoImovel.id != 3) && !vm.isSincSpiunet()\" ng-click=vm.showCancelarImovel($event)>Cancelar Imóvel</md-button><md-button class=\"md-primary md-raised\" ng-click=selectTab(0)>Avançar</md-button></div></ng-form></md-content></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-proprietario.tmpl.html","<div layout=row><div layout=column flex=30><div class=div-cimapa id=imagemPontoImovel><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=5 imovel=imovel action=add habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout-xs=column flex=70><md-content flex><ng-form name=forms.proprietarioForm><v-accordion multiple class=vAccordion--default style=\"margin-top: 20px\"><v-pane id=secaoProprietario><v-pane-header>Dados do Proprietário/Titular</v-pane-header><v-pane-content id=secaoProprietarioContent><dados-proprietario imovel=imovel is-detail=vm.isDetail></dados-proprietario></v-pane-content></v-pane></v-accordion></ng-form><div layout=row layout-align=\"end center\"><md-button md-no-ink class=\"md-raised md-secondary\" ng-click=vm.tabs.previousTab();>Retornar</md-button><md-button class=\"md-raised md-primary\" ng-if=\"imovel.situacaoImovel.id == 1\" ui-sref=\"INCORPORACAO.alterarCadastro({id: imovel.id, tab: 5})\" ng-click=\"vm.verificarRestricao(vm.imovel.rip, $event)\">Editar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=vm.tabs.selectTab(8)>Avançar</md-button></div></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-terreno-imovel.tmpl.html","<div layout=row><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=1 imovel=imovel action=detail habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/editar-imovel/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout-xs=column flex=70 ng-controller=\"TerrenoController as terrenoCtrl\"><md-content flex><ng-form name=forms.terrenoForm enctype=multipart/form-data><v-accordion name=acordionV class=vAccordion--default style=\"margin-top: 20px\"><v-pane id=accordionCartorial xpanded=true ng-show=\"imovel.incorporacao.matriculas.length > 0\"><v-pane-header ng-click=\"vm.validaSecao($event, forms.terrenoForm, 0, \'.secaoDadosDescricaoCartorial\')\">Descrição Cartorial</v-pane-header><v-pane-content><!--<div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/descricao-cartorial.tmpl.html\'\"></div>--><descricao-terreno-cartorial imovel=imovel tipos-geometria=terrenoCtrl.tiposDesenhoGeometriaTerreno action=\"\'detail\'\"></descricao-terreno-cartorial></v-pane-content></v-pane><v-pane><v-pane-header ng-click=\"vm.validaSecao($event, forms.terrenoForm, 0, \'.secaoDadosDescricaoCadastral\')\">Descrição Cadastral</v-pane-header><v-pane-content><descricao-terreno-cadastral imovel=imovel tipos-geometria=terrenoCtrl.tiposDesenhoGeometriaTerreno action=\"\'detail\'\"></descricao-terreno-cadastral><!--<div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/descricao-cadastral.tmpl.html\'\"></div>--></v-pane-content></v-pane><v-pane><v-pane-header ng-click=\"vm.validaSecao($event, forms.terrenoForm, 0, \'.secaoRestricoesUsoOcupacao\')\">Restrições de Uso/Ocupação</v-pane-header><v-pane-content><restricoes-uso-ocupacao terreno=imovel.terreno is-detail=true id-tipo-imovel=imovel.tipoImovel.id></restricoes-uso-ocupacao></v-pane-content></v-pane><v-pane><v-pane-header ng-click=\"vm.validaSecao($event, forms.terrenoForm, 0, \'.secaoArquivosComplementares\')\">Arquivos Complementares</v-pane-header><v-pane-content><div layout=row flex=100><div layout=column flex=100><div><upload-arquivos action=detail titulo=Documentos model=imovel.arquivosComplementaresLista></upload-arquivos></div></div></div></v-pane-content></v-pane></v-accordion><div layout=row layout-align=\"end center\"><md-button class=\"md-secondary md-raised\" ng-click=selectTab(0) onkeypress=selectTab(2)>Retornar</md-button><md-button class=\"md-raised md-secondary\" ui-sref=INCORPORACAO.imovelConsultar>Fechar</md-button><md-button class=\"md-raised md-primary\" ng-if=\"imovel.situacaoImovel.id == 1\" ui-sref=\"INCORPORACAO.alterarCadastro({id: imovel.id, tab: 1})\" ng-click=\"vm.verificarRestricao(vm.imovel.rip, $event)\">Editar</md-button><md-button class=\"md-primary md-raised\" ng-if=\"(vm.imovel.situacaoImovel.id != 3) && !vm.isSincSpiunet()\" ng-click=vm.showCancelarImovel($event)>Cancelar Imóvel</md-button><md-button ng-if=\"terrenoCtrl.isLoteComEdificacao() || terrenoCtrl.isEspelhoDagua()\" class=\"md-primary md-raised\" ng-click=selectTab(2) onkeypress=selectTab(2)>Avançar</md-button><md-button ng-if=terrenoCtrl.isTipoImovelUnidadeAutonoma() class=\"md-primary md-raised\" ng-click=selectTab(3) onkeypress=selectTab(3)>Avançar</md-button><md-button ng-if=\"!terrenoCtrl.isTipoImovelUnidadeAutonoma() && !(terrenoCtrl.isLoteComEdificacao() || terrenoCtrl.isEspelhoDagua())\" class=\"md-primary md-raised\" ng-click=selectTab(5) onkeypress=selectTab(5)>Avançar</md-button></div></ng-form></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/tabs/tab-unidade-autonoma.tmpl.html","<div layout=row><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=3 imovel=imovel action=detail habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/editar-imovel/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout=column flex=70 layout-xs=column><md-content flex><ng-form name=adicionarUnidadeAutonomaForm><v-accordion class=vAccordion--default style=\"margin-top: 20px\"><v-pane expanded ng-if=!vm.isGaragem><v-pane-header ng-click=\"vm.validaSecao($event, adicionarUnidadeAutonomaForm, 3, \'.secaoDadosEdificacao\')\">Dados da Edificação</v-pane-header><v-pane-content><dados-edificacao-unidade-autonoma action=\"\'detail\'\" unidade-autonoma=vm.imovel.unidadeAutonoma enderecamento=vm.imovel.enderecamento forma-cadastro-edificacao=vm.tipoCadastroEdificacaoUnidadeAutonoma show-tab-form-documento=vm.mostraTabFormDocumento documento=vm.documento documento-backup=vm.documentoBackup></dados-edificacao-unidade-autonoma></v-pane-content></v-pane><v-pane ng-if=!vm.isGaragem><v-pane-header ng-click=\"vm.validaSecao($event, adicionarUnidadeAutonomaForm, 3, \'.secaoDadosUA\')\">Dados da Unidade Autônoma</v-pane-header><v-pane-content><dados-unidade-autonoma imovel=vm.imovel action=\"\'detail\'\" tipo-imovel=vm.imovel.tipoImovel unidade-autonoma=vm.imovel.unidadeAutonoma></dados-unidade-autonoma></v-pane-content></v-pane><v-pane expanded ng-if=vm.isGaragem><v-pane-header ng-click=\"vm.validaSecao($event, forms.adicionarUnidadeAutonomaForm, 3, \'.secaoDadosGaragem\')\">Dados da Garagem</v-pane-header><v-pane-content><dados-garagem-unidade-autonoma unidade-autonoma=vm.imovel.unidadeAutonoma action=\"\'detail\'\"></dados-garagem-unidade-autonoma></v-pane-content></v-pane></v-accordion></ng-form><div layout=row layout-align=\"end right\"><md-button class=\"md-secondary md-raised\" ng-click=selectTab(1)>Retornar</md-button><md-button class=\"md-raised md-primary\" ng-if=\"imovel.situacaoImovel.id == 1\" ui-sref=\"INCORPORACAO.alterarCadastro({id: imovel.id, tab: 3})\" ng-click=\"vm.verificarRestricao(vm.imovel.rip, $event)\">Editar</md-button><md-button class=\"md-primary md-raised\" ng-if=\"(vm.imovel.situacaoImovel.id != 3) && !vm.isSincSpiunet()\" ng-click=vm.showCancelarImovel($event)>Cancelar Imóvel</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=selectTab(5)>Avançar</md-button></div></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/album-fotos/album-fotos.html","<div layout=column><!--  <md-button class=\" md-primary md-raised\" ng-if=\"!vm.isDetail\" ng-click=\"vm.showUploadImagemDialog($event)\"\n		aria-label=\"upload foto\"> Adicionar foto </md-button>--><inc-carousel imagens=imovel.imagens src=true link-imagem=\"vm.showModalImagemDialog(ev, 0)\"></inc-carousel><!--<table ng-show=\"imovel.imagens.length>0\" class=\"mui-table\" style=\"width: 100% !important;\">\n    <thead>\n    <div class=\"barra-separadora\" ng-show=\"imovel.imagens.length>0\"></div>\n    </thead>\n    <tbody>\n    <tr ng-repeat=\"img in imovel.imagens\" ng-model-options=\"{trackBy: \'$value.id\'}\" style=\"text-align: center;\">\n      <td>{{img.descricao}}</td>\n      <td>{{img.data | date : \'dd/MM/yyyy\'}}</td>\n      <td>\n        <ng-md-icon ng-hide=\"selectedIndex == 1\" icon=\"delete\" ng-if=\"!vm.isDetail\" style=\"fill:#788489\" size=\"18\" alt=\"Excluir\" ng-click=\"vm.removerImagem($index)\"></ng-md-icon>\n      </td>\n    </tr>\n    </tbody>\n  </table>--></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/album-fotos/dialog-upload-fotos.html","<md-dialog aria-label=\"Upload de Imagens\" flex=60 ng-cloak><form name=uploadImagem ng-submit=incluir()><md-toolbar><div class=md-toolbar-tools><h2>Upload de Imagem</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><md-input-container class=md-block flex-gt-sm><label>Descrição</label><input ng-model=imagem.descricao name=fotoDescricao type=text required> <span class=form-error ng-show=\"uploadImagem.fotoDescricao.$error.required && submitted\">Campo de preenchimento obrigatório</span></md-input-container><div layout-gt-sm=column><md-datepicker ng-mask=39/19/9999 restrict=reject ng-model=imagem.data md-placeholder=\"Data da foto\" required md-max-date=uploadImagemDialogEditarCtrl.hoje name=fotoData></md-datepicker><span class=form-error ng-show=\"uploadImagem.fotoData.$error.required && submitted\">Campo de preenchimento obrigatório</span></div></div><div layout-gt-sm=row layout-margin><input type=file ngf-select ng-model=imagem.imagem accept=\".bmp, .png, .jpg, .jpeg\" ngf-max-size=5MB ngf-model-invalid=errorFile required name=fotoImagem> <span class=form-error ng-show=\"uploadImagem.fotoImagem.$error.required && submitted\">Campo de preenchimento obrigatório</span></div><div layout-gt-sm=row layout-margin layout-align=\"end right\"><md-button type=submit layout-align=right class=\"md-primary md-raised\" ng-disabled=uploadImagem.$invalid>Inserir</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-benfeitoria.html","<div layout=row><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=2 imovel=imovel action=edit habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/editar-imovel/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout=column flex=70><div layout=column><div layout-margin>Acessão/Benfeitoria pertence ao proprietário? *</div><md-radio-group ng-model=vm.imovel.benfeitoriaPertenceAoProprietario ng-disabled=!vm.podeEditar><div layout=row style=\"margin: 0 20px\"><md-radio-button value=Sim flex>Sim</md-radio-button><md-radio-button value=Não flex>Não</md-radio-button><md-radio-button value=\"Sem Informação\" flex>Sem Informação</md-radio-button></div></md-radio-group></div><div layout=row layout-align=\"start center\"><md-button class=md-primary ng-click=vm.exibirTabInclusaoBenfeitoria(); ng-show=vm.podeEditar>Adicionar Acessão/Benfeitoria</md-button><md-button class=md-primary ng-click=modalBenfeitoriasCanceladas($event) ng-show=\"benfeitoriasCanceladas.length > 0\">Detalhar Acessões/Benfeitorias Canceladas</md-button></div><div layout=row layout-xs=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>Benfeitoria</th><th md-column>Detalhes</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-click=destacaBenfeitoriaMapa(item.codigo); ng-blur=destacaBenfeitoriaMapa(); ng-repeat=\"item in imovel.benfeitorias | filter: {ativa : true} | limitTo: vm.paginacaoBenfeitorias.limit: (vm.paginacaoBenfeitorias.page - 1) * vm.paginacaoBenfeitorias.limit\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-show=item.ativa><td md-cell><img ng-src=\"{{item.imagens[0].imagem != undefined ? \'data:image/jpeg;base64,\'+item.imagens[0].imagem :\'./assets/img/no-image.png\'}}\" style=\"width: 100px; height: 75px\"></td><td md-cell><p><span style=\"font-weight: bold\">ID:</span> {{item.id}}</p><p><span style=\"font-weight: bold\">Código:</span> {{item.codigo}}</p><p ng-show=\"item.codigo.startsWith(\'E\')\"><span style=\"font-weight: bold\">Tipo de Acessão/Benfeitoria:</span> {{item.edificacao.tipoEspecializacaoEdificacao.descricao}}</p><p ng-show=\"item.codigo.startsWith(\'C\')\"><span style=\"font-weight: bold\">Tipo de Acessão/Benfeitoria:</span> {{item.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao}}</p><p ng-if=\"item.tipoBenfeitoria.id === 1\"><span style=\"font-weight: bold\">Nome:</span> {{item.nome}}</p><p><span style=\"font-weight: bold\">Área Construída(m²):</span> {{item.areaConstruida | number}}</p></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"modalCancelarBenfeitoria($event, item.id, $index)\" class=\"md-fab md-raised md-mini\" aria-label=\"Cancelar Acessão/Benfeitoria\" ng-show=vm.podeEditar><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Cancelar Acessão/Benfeitoria</md-tooltip></md-icon></md-button><md-button ng-click=vm.exibirTabInclusaoBenfeitoria(item) class=\"md-fab md-raised md-mini\" aria-label=\"Editar Acessão/Benfeitoria\" ng-show=vm.podeEditar><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Acessão/Benfeitoria</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=vm.paginacaoBenfeitorias.limit md-limit-options=vm.paginacaoBenfeitorias.limitOptions md-page=vm.paginacaoBenfeitorias.page md-total=\"{{ vm.totalBenfeitoriasAtivas() }}\" md-page-select ng-disabled=\"vm.imovel.benfeitorias.length < 6\"></md-table-pagination></md-content></div><div layout=row layout-align=\"end right\"><md-button md-no-ink class=\"md-secondary md-raised\" ng-click=selectTab(1)>Retornar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=\"vm.submitFormularioDadosGerais(\'salvar\')\">Gravar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=selectTab(5)>Avançar</md-button></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-imovel.html","<style></style><div layout=row><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=0 imovel=imovel action=edit habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/editar-imovel/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div column=column flex=70><md-content><ng-form name=forms.imovelForm><v-accordion class=vAccordion--default><v-pane id=detalhesDoImovel expanded><v-pane-header ng-click=\"vm.validaSecao($event, forms.imovelForm, 0, \'.secaoDetalhesImovelEditar\')\">Detalhes do imóvel</v-pane-header><v-pane-content id=detalhesDoImovelContent><secao-detalhes-imovel imovel=imovel is-detail=false desabilitar=!vm.podeEditar></secao-detalhes-imovel></v-pane-content></v-pane><v-pane id=dadosIncorporacao><v-pane-header ng-click=\"vm.validaSecao($event, forms.imovelForm, 0, \'.secaoDadosIncorporacaoEditar\')\">Dados de Incorporação</v-pane-header><v-pane-content id=dadosIncorporacaoContent><secao-dados-incorporacao imovel=imovel is-exibe-tab-titulo-aquisitivo=vm.mostraTabTituloAquisitivo titulo=vm.titulo titulo-backup=vm.tituloBackup query-pagination=vm.paginationTituloAquisitivo action=\"\'edit\'\" desabilitar=!vm.podeEditar></secao-dados-incorporacao></v-pane-content></v-pane><v-pane><v-pane-header>Classificação de Utilização</v-pane-header><v-pane-content><secao-classificacao-utilizacao imovel=imovel is-detail=!vm.podeEditar></secao-classificacao-utilizacao></v-pane-content></v-pane><v-pane><v-pane-header>Fotos do Imóvel</v-pane-header><v-pane-content><upload-fotos model=imovel.imagens desabilitar=!vm.podeEditar></upload-fotos></v-pane-content></v-pane></v-accordion></ng-form><div layout=row layout-align=\"end right\"><md-button md-no-ink class=\"md-secondary md-raised\" ng-click=selectTab(-1)>Retornar</md-button><md-button md-no-ink class=\"md-secondary md-raised\" ng-click=vm.fecharEdicao()>Fechar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=\"vm.submitFormularioDadosGerais(\'salvar\')\">Gravar</md-button><md-button id=avancarDadosGerais class=\"md-primary md-raised\" ng-click=vm.submitFormularioDadosGerais()>Avançar</md-button></div></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-localizacao.tmpl.html","<div layout-xs=column ng-controller=\"LocalizacaoController as ctrl\"><md-content flex><ng-form name=forms.localizacaoForm><inc-localizacao-imovel imovel=vm.imovel action=\"\'edit\'\" desabilitar=!vm.podeEditar></inc-localizacao-imovel><div layout=row layout-align=\"end center\"><md-button md-no-ink class=\"md-secondary md-raised\" ng-if=\"vm.imovel.situacaoImovel.id === 2 && vm.imovel.situacaoCadastro.id === 3\" ng-click=selectTab(-2)>Retornar</md-button><md-button md-no-ink class=\"md-secondary md-raised\" ng-click=vm.fecharEdicao()>Fechar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=\"showModalValidacao($event, forms.localizacaoForm.$invalid, -1, \'salvar\')\">Gravar</md-button><md-button id=btnAvancarTabLocalizacao class=\"md-primary md-raised\" ng-click=\"showModalValidacao($event, forms.localizacaoForm.$invalid, 0, null)\">Avançar</md-button></div></ng-form></md-content></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-proprietario.html","<div layout=row><div layout=column flex=30><div class=div-cimapa id=imagemPontoImovel><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=5 imovel=imovel action=add habilitar-desenho=false></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout-xs=column flex=70><md-content flex><ng-form name=forms.proprietarioForm><v-accordion multiple class=vAccordion--default style=\"margin-top: 20px\"><v-pane id=secaoProprietario expanded><v-pane-header>Dados do Proprietário/Titular</v-pane-header><v-pane-content id=secaoProprietarioContent><dados-proprietario imovel=imovel is-detail=vm.isDetail desabilitar=!vm.podeEditar></dados-proprietario></v-pane-content></v-pane></v-accordion></ng-form><div layout=row layout-align=\"end center\"><md-button md-no-ink class=\"md-secondary md-raised\" ng-click=vm.tabs.previousTab();>Retornar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=\"showModalMotivo($event, forms.proprietarioForm.$invalid, null, \'salvar\')\">{{vm.temPermissaoParaHomologarImovel() ? \'Gravar\' : \'Enviar para Homologação\'}}</md-button></div></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-terreno.html","<div layout=row><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=1 imovel=imovel action=edit habilitar-desenho=\"imovel.terreno.tipoGeometriaCadastral == \'VETORIZACAO\' && vm.podeEditar\"></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/editar-imovel/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout-xs=column flex=70 ng-controller=\"TerrenoController as terrenoCtrl\"><md-content flex><ng-form name=forms.terrenoForm enctype=multipart/form-data><v-accordion class=vAccordion--default style=\"margin-top: 20px\"><v-pane ng-show=\"imovel.incorporacao.matriculas.length > 0\"><v-pane-header ng-click=\"vm.validaSecao($event, forms.terrenoForm, 0, \'.secaoDadosDescricaoCartorialEditar\')\">Descrição Cartorial<md-icon ng-bind=\"\'info_outline\'\" ng-if=vm.editandoUA()><md-tooltip>Dados atualizados nas demais UAs</md-tooltip></md-icon></v-pane-header><v-pane-content><descricao-terreno-cartorial imovel=imovel tipos-geometria=terrenoCtrl.tiposDesenhoGeometriaTerreno action=\"\'edit\'\" desabilitar=!vm.podeEditar></descricao-terreno-cartorial></v-pane-content></v-pane><v-pane id=descricaoCadastral><v-pane-header ng-click=\"vm.validaSecao($event, forms.terrenoForm, 0, \'.secaoDadosDescricaoCadastralEditar\')\">Descrição Cadastral<md-icon ng-bind=\"\'info_outline\'\" ng-if=vm.editandoUA()><md-tooltip>Dados atualizados nas demais UAs</md-tooltip></md-icon></v-pane-header><v-pane-content id=descricaoCadastralContent><descricao-terreno-cadastral imovel=imovel tipos-geometria=terrenoCtrl.tiposDesenhoGeometriaTerreno action=\"\'edit\'\" desabilitar=!vm.podeEditar></descricao-terreno-cadastral></v-pane-content></v-pane><v-pane><v-pane-header ng-click=\"vm.validaSecao($event, forms.terrenoForm, 0, \'.secaoRestricoesUsoOcupacaoEditar\')\">Restrições de Uso/Ocupação<md-icon ng-bind=\"\'info_outline\'\" ng-if=vm.editandoUA()><md-tooltip>Dados atualizados nas demais UAs</md-tooltip></md-icon></v-pane-header><v-pane-content><restricoes-uso-ocupacao terreno=imovel.terreno is-detail=false id-tipo-imovel=imovel.tipoImovel.id desabilitar=!vm.podeEditar></restricoes-uso-ocupacao></v-pane-content></v-pane><v-pane><v-pane-header ng-click=\"vm.validaSecao($event, forms.terrenoForm, 0, \'.secaoArquivosComplementares\')\">Arquivos Complementares</v-pane-header><v-pane-content><!--div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/arquivos-complementares.html\'\"></div>--><div layout=row flex=100><div layout=column flex=100><div><upload-arquivos model=imovel.arquivosComplementaresLista titulo=Documentos desabilitar=!vm.podeEditar></upload-arquivos></div></div></div></v-pane-content></v-pane></v-accordion><div layout=row layout-align=\"end right\"><md-button md-no-ink class=\"md-secondary md-raised\" ng-click=selectTab(0)>Retornar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=\"vm.submitFormularioTerreno(); vm.submitFormularioDadosGerais(\'salvar\')\">Gravar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=vm.submitFormularioTerreno()>Avançar</md-button></div></ng-form></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/tabs/tab-unidade-autonoma.html","<div layout=row ng-show=vm.showFormularioUnidadeAutonoma()><div layout=column flex=30><div class=div-cimapa><ci-mapa-incorporacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=3 imovel=imovel action=edit edificacoes=vm.edificacoesUnidadeAutonoma habilitar-desenho=vm.tipoVetorizacao()></ci-mapa-incorporacao></div><div><fieldset class=fieldset style=\"margin-top: 15px\"><legend>Álbum de fotos</legend><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/editar-imovel/album-fotos/album-fotos.html\'\"></div></fieldset></div></div><div layout=column flex=70 layout-xs=column><md-content flex><ng-form name=forms.adicionarUnidadeAutonomaForm><v-accordion class=vAccordion--default style=\"margin-top: 20px\"><v-pane expanded ng-if=!vm.isGaragem><v-pane-header ng-click=\"vm.validaSecao($event, adicionarUnidadeAutonomaForm, 3, \'.secaoDadosEdificacaoEditar\')\">Dados da Edificação<md-icon ng-bind=\"\'info_outline\'\" ng-if=vm.editandoUA()><md-tooltip>Dados atualizados nas demais UAs</md-tooltip></md-icon></v-pane-header><v-pane-content><dados-edificacao-unidade-autonoma unidade-autonoma=vm.imovel.unidadeAutonoma enderecamento=vm.imovel.enderecamento forma-cadastro-edificacao=vm.tipoCadastroEdificacaoUnidadeAutonoma show-tab-form-documento=vm.mostraTabFormDocumento documento-backup=vm.documentoBackup documento=vm.documento desabilitar=!vm.podeEditar></dados-edificacao-unidade-autonoma></v-pane-content></v-pane><v-pane expanded ng-if=vm.isGaragem><v-pane-header ng-click=\"vm.validaSecao($event, adicionarUnidadeAutonomaForm, 3, \'.secaoDadosGaragemEditar\')\">Dados da Garagem</v-pane-header><v-pane-content><dados-garagem-unidade-autonoma unidade-autonoma=vm.imovel.unidadeAutonoma desabilitar=!vm.podeEditar></dados-garagem-unidade-autonoma></v-pane-content></v-pane><v-pane ng-if=\"!vm.isGaragem && vm.tipoCadastroEdificacaoUnidadeAutonoma\"><v-pane-header ng-click=\"vm.validaSecao($event, adicionarUnidadeAutonomaForm, 3, \'.secaoDadosUAEditar\')\">Dados da Unidade Autônoma</v-pane-header><v-pane-content><dados-unidade-autonoma imovel=vm.imovel tipo-imovel=vm.imovel.tipoImovel unidade-autonoma=vm.imovel.unidadeAutonoma desabilitar=!vm.podeEditar></dados-unidade-autonoma></v-pane-content></v-pane></v-accordion></ng-form><div layout=row layout-align=\"end right\"><md-button md-no-ink class=\"md-secondary md-raised\" ng-click=vm.tabs.previousTab();>Retornar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=\"vm.submitFormularioUnidadeAutonoma()? vm.submitFormularioDadosGerais(\'salvar\'):\'\'\">Gravar</md-button><md-button md-no-ink class=\"md-primary md-raised\" ng-click=vm.submitFormularioUnidadeAutonoma()>Avançar</md-button></div></md-content></div></div><!-- <div layout=\"column\" ng-if=\"vm.tipoCadastroEdificacaoUnidadeAutonoma === \'EXISTENTE\' && !vm.imovel.unidadeAutonoma.edificacao.id\"> --><div layout=column ng-if=\"vm.showMapaEdificacaoCadastrada == true\"><selecao-edificacao-unidade-autonoma edificacao=vm.imovel.unidadeAutonoma.edificacao condicoes-acessibilidade=vm.imovel.unidadeAutonoma.condicoesAcessibilidade terreno=vm.imovel.terreno on-voltar=vm.cancelarSelecaoEdificacaoUnidadeAutonoma() edificacoes=vm.edificacoesUnidadeAutonoma></selecao-edificacao-unidade-autonoma></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/modals/selecionar-rips/dialog-selecionar-rips.html","<md-dialog aria-label=\"Selecionar Imoveis\" flex=80 ng-cloak><form name=selecionarRIPsForm><md-toolbar><div class=md-toolbar-tools><h2>Selecionar Imóveis</h2><span flex></span><md-button class=md-icon-button ng-click=selecionarRIPsCtrl.fechar();><md-icon md-svg-src=assets/img/icons/ic_close_white_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><md-input-container class=md-block flex-gt-sm><label>País</label><md-select ng-model=selecionarRIPsCtrl.filtroRIPs.enderecamento.pais name=pais ng-change=selecionarRIPsCtrl.buscarCidadesPorPais(); ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option ng-repeat=\"item in selecionarRIPsCtrl.paises | orderByBr:\'descricao\'\" ng-value=item ng-selected=\"item.id == 1\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs ng-show=\"selecionarRIPsCtrl.filtroRIPs.enderecamento.pais.id === 1\"><label>CEP</label><input ng-model=selecionarRIPsCtrl.filtroRIPs.enderecamento.cep ui-br-cep-mask ng-change=selecionarRIPsCtrl.buscarEnderecoCep();></md-input-container><md-input-container><label id=labelUf>UF</label><!--\n                        <md-select ng-model=\"selecionarRIPsCtrl.filtroRIPs.enderecamento.uf\" ng-disabled=\"selecionarRIPsCtrl.isCEPPreenchido\" name=\"uf\" ng-change=\"selecionarRIPsCtrl.buscarMunicipiosPorUf()\">\n                            <md-option ng-repeat=\"uf in selecionarRIPsCtrl.ufs\" ng-value=\"uf.sigla\">\n                                {{uf.descricao}}\n                            </md-option>\n                        </md-select>\n                    --><md-autocomplete name=uf ng-disabled=selecionarRIPsCtrl.isCEPPreenchido md-clear-button=!$mdAutocompleteCtrl.isDisabled md-selected-item=selecionarRIPsCtrl.filtroRIPs.enderecamento.uf md-selected-item-change=selecionarRIPsCtrl.buscarMunicipiosPorUf() md-search-text=$scope.searchUf md-items=\"item in selecionarRIPsCtrl.ufs | filter:{descricao:$scope.searchUf} | orderByBr:\'descricao\'\" md-item-text=item.descricao md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=labelUf><md-item-template><span md-highlight-text=$scope.searchUf md-highlight-flags=^i>{{item.descricao}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchUf}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container></div><div layout-gt-sm=row layout-margin><md-input-container class=md-block ng-show=\"selecionarRIPsCtrl.filtroRIPs.enderecamento.pais.id === 1\" flex-gt-sm flex=50><label labelmunicipio>Município</label><!--\n                        <md-select ng-model=\"selecionarRIPsCtrl.filtroRIPs.enderecamento.municipio\" name=\"municipio\" ng-disabled=\"selecionarRIPsCtrl.municipios.length == 0 || selecionarRIPsCtrl.isCEPPreenchido\">\n                            <md-option ng-repeat=\"item in selecionarRIPsCtrl.municipios\" ng-value=\"item.nome ? item.nome : item\">\n                                {{item.nome ? item.nome : item}}\n                            </md-option>\n                        </md-select>\n                    --><md-autocomplete md-selected-item=selecionarRIPsCtrl.filtroRIPs.enderecamento.municipio name=municipio ng-disabled=\"selecionarRIPsCtrl.municipios.length == 0 || selecionarRIPsCtrl.isCEPPreenchido\" md-clear-button=!$mdAutocompleteCtrl.isDisabled md-search-text=$scope.searchMunicipio md-items=\"item in selecionarRIPsCtrl.municipios | filter:{name:$scope.searchMunicipio} | orderByBr:\'name\'\" md-item-text=\"item.nome ? item.nome\" md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=labelMunicipio><md-item-template><span md-highlight-text=$scope.searchMunicipio md-highlight-flags=^i>{{item.nome ? item.nome : item}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchMunicipio}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block flex-gt-sm ng-show=\"selecionarRIPsCtrl.filtroRIPs.enderecamento.pais.id != 1\"><label>Cidade Exterior</label><md-select ng-model=selecionarRIPsCtrl.filtroRIPs.enderecamento.cidadeExterior name=cidadeExterior><md-option ng-repeat=\"item in selecionarRIPsCtrl.cidades\" ng-value=item>{{item}}</md-option></md-select></md-input-container></div><div layout-gt-sm=row layout-margin layout-align=\"center center\"><md-button class=\"md-raised md-primary\" ng-click=selecionarRIPsCtrl.pesquisar()>Pesquisar</md-button><md-button class=\"md-raised md-primary\" ng-click=selecionarRIPsCtrl.limpar()>Limpar</md-button></div><div layout=row layout-align=\"center stretch\" ng-show=\"selecionarRIPsCtrl.resultadoPesquisa.length > 0\" ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/modals/selecionar-rips/lista-selecionar-rips.html\'\"></div><div layout-gt-sm=row layout-align=end ng-show=\"selecionarRIPsCtrl.resultadoPesquisa.length > 0\"><md-button class=\"md-raised md-primary\" ng-click=selecionarRIPsCtrl.confirmar()>Confirmar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/modals/selecionar-rips/lista-selecionar-rips.html","<div flex-gt-sm><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<md-checkbox style=\"background-color: rgba(255, 255, 255, 255)\" ng-model=selecionarRIPsCtrl.selecionarTodos ng-change=selecionarRIPsCtrl.marcarDesmarcarTodosCheckbox();></md-checkbox></th><th md-column style=\"color: #fff; font-size: 14px; text-align: center\">Detalhes</th><th></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in selecionarRIPsCtrl.resultadoPesquisa\"><td md-cell>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<md-checkbox aria-label={{item.rip}} ng-model=item.isSelecionado ng-change=selecionarRIPsCtrl.atualizaMarcarTodosCheckbox(item);></md-checkbox></td><td md-cell class=shine><p style=\"padding: 0 15px\"><span style=\"margin-left: 10px\">RIP:&nbsp; </span><span style=\"font-weight: bold\">{{item.rip}}</span></p><p style=\"padding: 0 15px\" ng-show=\"item.enderecoDTO.pais == \'Brasil\'\"><span style=\"margin-left: 10px\">Endereço: </span><span style=\"font-weight: bold\">{{item.enderecoDTO.tipoLogradouro}} {{item.enderecoDTO.logradouro}}, Número {{item.enderecoDTO.numero}}, {{item.enderecoDTO.bairro}}, {{item.enderecoDTO.municipio}}/{{item.enderecoDTO.uf}}-{{item.enderecoDTO.pais}}. CEP: {{item.enderecoDTO.cep}}</span></p><p style=\"padding: 0 15px\" ng-show=\"item.enderecoDTO.pais != \'Brasil\'\"><span style=\"margin-left: 10px\">Endereço: </span><span style=\"font-weight: bold\">{{item.enderecoDTO.endereco}}, {{item.enderecoDTO.complemento}}, {{item.enderecoDTO.cidadeExterior}} - {{item.enderecoDTO.pais}}. Código Postal: {{item.enderecoDTO.codigoPostal}}.</span></p><p style=\"padding: 0 15px\"><span style=\"margin-left: 10px\">Matricula/Transcrição: </span><span style=\"font-weight: bold\">{{item.numeroMatricula}}</span></p><p style=\"padding: 0 15px\"><span style=\"margin-left: 10px\">Proprietário/Titular: </span><span style=\"font-weight: bold\">{{item.tipoProprietarioReal}}</span></p></td><td md-cell><a style=\"text-decoration: none\" target=_blank ng-href=/#/incorporacao/detalharImovel/{{item.id}}><ng-md-icon icon=search style=fill:#788489 size=22 alt=\"Detalhar imóvel\"><md-tooltip>Detalhar imóvel</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=selecionarRIPsCtrl.query.limit md-limit-options=limitOptions md-page=selecionarRIPsCtrl.query.page md-total={{selecionarRIPsCtrl.query.totalElements}} md-on-paginate=selecionarRIPsCtrl.page md-page-select></md-table-pagination></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/condicoes-de-acessibilidade.tmpl.html","<md-table-container ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><table md-table flex border=2px class=tabela-acessibilidade><thead><tr md-row><th colspan=4>Condições de Acessibilidade</th></tr><tr md-row><th style=\"width: 60%\">Campo</th><th style=\"width: 16%\">Original</th><th style=\"width: 16%\">Alterado/Novo</th><th style=\"width: 8%\">Recusar</th></tr></thead><tbody><tr md-row><td md-cell>Há vagas próximas dos acessos de circulação de pedestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</td><td md-cell><span ng-show=imovelOriginal.exibir><span ng-show=item.imovelOriginal.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelOriginal.edificacao.vagasSinalizadas.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaCondicoesAcessibilidadeAlterado(item.imovelOriginal.edificacao.vagasSinalizadas.descricao, item.imovelAlterado.edificacao.vagasSinalizadas.descricao, item)\"><span ng-show=item.imovelAlterado.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelAlterado.edificacao.vagasSinalizadas.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"isDesabilitarCheckboxAcessibilidade(item.imovelOriginal.edificacao.vagasSinalizadas.descricao, item.imovelAlterado.edificacao.vagasSinalizadas.descricao, item)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.vagasSinalizadas ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row><td md-cell>Os acessos ao interior da edificação estão livres de barreiras arquitetónicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=imovelOriginal.exibir><span ng-show=item.imovelOriginal.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelOriginal.edificacao.acessoLivreObstaculos.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaCondicoesAcessibilidadeAlterado(item.imovelOriginal.edificacao.acessoLivreObstaculos.descricao, item.imovelAlterado.edificacao.acessoLivreObstaculos.descricao, item)\">{{item.imovelAlterado.edificacao.acessoLivreObstaculos.descricao}} <span ng-show=item.imovelAlterado.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade.</span></td><td md-cell><md-checkbox ng-disabled=\"isDesabilitarCheckboxAcessibilidade(item.imovelOriginal.edificacao.acessoLivreObstaculos.descricao, item.imovelAlterado.edificacao.acessoLivreObstaculos.descricao, item)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.acessoLivreObstaculos ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row><td md-cell>Há banheiro acessível, com seus equipamentos e acessórios distribuidos de maneira que possam ser utilizafos por pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=item.imovelOriginal.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.banheiroAcessivel.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaCondicoesAcessibilidadeAlterado(item.imovelOriginal.edificacao.banheiroAcessivel.descricao, item.imovelAlterado.edificacao.banheiroAcessivel.descricao, item)\"><span ng-show=item.imovelAlterado.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelAlterado.edificacao.banheiroAcessivel.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"isDesabilitarCheckboxAcessibilidade(item.imovelOriginal.edificacao.banheiroAcessivel.descricao, item.imovelAlterado.edificacao.banheiroAcessivel.descricao, item)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.banheiroAcessivel ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row><td md-cell>Os elevadores oferecem condições de acessibilidade para pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=imovelOriginal.exibir><span ng-show=item.imovelOriginal.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelOriginal.edificacao.elevadorAcessivel.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaCondicoesAcessibilidadeAlterado(item.imovelOriginal.edificacao.elevadorAcessivel.descricao, item.imovelAlterado.edificacao.elevadorAcessivel.descricao, item)\"><span ng-show=item.imovelAlterado.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelAlterado.edificacao.elevadorAcessivel.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"isDesabilitarCheckboxAcessibilidade(item.imovelOriginal.edificacao.elevadorAcessivel.descricao, item.imovelAlterado.edificacao.elevadorAcessivel.descricao, item)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.elevadorAcessivel ng-true-value=true ng-false-value=false></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/dados-acessao-benfeitoria.tmpl.html","<md-table-container><table md-table flex border=2px class=tabela-dados><thead><tr md-row><th colspan=4>Dados da Acessão/Benfeitoria</th></tr><tr md-row ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/detalhar-imovel-label-cabecalho.tmpl.html\'\"></tr></thead><tbody><tr md-row><td md-cell>Tipo de Acessão / Benfeitoria</td><td md-cell><span ng-show=imovelOriginal.exibir><span ng-show=item.imovelOriginal.edificacao>Edificação / {{item.imovelOriginal.edificacao.tipoEspecializacaoEdificacao.descricao}} </span><span ng-show=item.imovelOriginal.benfeitoriaComplementar>Complementar / {{item.imovelOriginal.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao}}</span></span></td><td md-cell ng-class=\"alterouTipoBenfeitoria(item) ? \'dado-alterado\' : null\"><span ng-show=item.imovelAlterado.edificacao ng-class=\"item.imovelOriginal.edificacao ? null : \'dado-alterado\'\">Edificação / {{item.imovelAlterado.edificacao.tipoEspecializacaoEdificacao.descricao}} </span><span ng-show=item.imovelAlterado.benfeitoriaComplementar ng-class=\"item.imovelAlterado.benfeitoriaComplementar ? null : \'dado-alterado\'\">Complementar / {{item.imovelAlterado.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao}}</span></td><td md-cell><md-checkbox ng-disabled=\"imovelOriginal.id !== imovelAlterado.id && !alterouTipoBenfeitoria(item)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.tipoBenfeitoria ng-true-value=null ng-false-value={}></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Nome</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.nome}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.nome, item.imovelAlterado.nome, item)\">{{item.imovelAlterado.nome}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.nome, item.imovelAlterado.nome)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.nome ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row><td md-cell>Área Construída</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.areaConstruida}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.areaConstruida, item.imovelAlterado.areaConstruida, item)\">{{item.imovelAlterado.areaConstruida}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.areaConstruida, item.imovelAlterado.areaConstruida)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.areaConstruida ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Área de Projeção</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.areaProjecao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.areaProjecao, item.imovelAlterado.edificacao.areaProjecao, item)\">{{item.imovelAlterado.edificacao.areaProjecao}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.areaProjecao, item.imovelAlterado.edificacao.areaProjecao)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.areaProjecao ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><!--alterado de taxa de ocupação para Fração de Ocupação--><td md-cell>Fração de Ocupação</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.taxaOcupacao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.taxaOcupacao, item.imovelAlterado.edificacao.taxaOcupacao, item)\">{{item.imovelAlterado.edificacao.taxaOcupacao}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.taxaOcupacao, item.imovelAlterado.edificacao.taxaOcupacao)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.taxaOcupacao ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Índice de Aproveitamento</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.indiceAproveitamento}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.indiceAproveitamento, item.imovelAlterado.edificacao.indiceAproveitamento, item)\">{{item.imovelAlterado.edificacao.indiceAproveitamento}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.indiceAproveitamento, item.imovelAlterado.edificacao.indiceAproveitamento)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.indiceAproveitamento ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Número de Pavimentos</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.numeroPavimentos}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.numeroPavimentos, item.imovelAlterado.edificacao.numeroPavimentos, item)\">{{item.imovelAlterado.edificacao.numeroPavimentos}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.numeroPavimentos, item.imovelAlterado.edificacao.numeroPavimentos)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.numeroPavimentos ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Número de Dormitórios</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.numeroDormitorios}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.numeroDormitorios, item.imovelAlterado.edificacao.numeroDormitorios, item)\">{{item.imovelAlterado.edificacao.numeroDormitorios}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.numeroDormitorios, item.imovelAlterado.edificacao.numeroDormitorios)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.numeroDormitorios ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Número de Unidades Residenciais</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.numeroUnidadesResidenciais}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.numeroUnidadesResidenciais, item.imovelAlterado.edificacao.numeroUnidadesResidenciais, item)\">{{item.imovelAlterado.edificacao.numeroUnidadesResidenciais}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.numeroUnidadesResidenciais, item.imovelAlterado.edificacao.numeroUnidadesResidenciais)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.numeroUnidadesResidenciais ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Averbada na Matrícula do Imóvel</td><td md-cell><span ng-show=imovelOriginal.exibir>{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.averbada)}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.averbada, item.imovelAlterado.edificacao.averbada, item)\">{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.averbada)}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.averbada), homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.averbada))\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.averbada ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Possui Habite-se</td><td md-cell><span ng-show=imovelOriginal.exibir>{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiHabitese)}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.possuiHabitese, item.imovelAlterado.edificacao.possuiHabitese, item)\">{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiHabitese)}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiHabitese), homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiHabitese))\" aria-label=dadosBenfeitoria aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.possuiHabitese ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Ano</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.anoHabitese}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.anoHabitese, item.imovelAlterado.edificacao.anoHabitese, item)\">{{item.imovelAlterado.edificacao.anoHabitese}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.anoHabitese, item.imovelAlterado.edificacao.anoHabitese)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.anoHabitese ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Possui Garagem</td><td md-cell><span ng-show=imovelOriginal.exibir>{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiGaragem)}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.possuiGaragem, item.imovelAlterado.edificacao.possuiGaragem, item)\">{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiGaragem)}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiGaragem), homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiGaragem))\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.possuiGaragem ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Número de Vagas</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.numeroVagas}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.numeroVagas, item.imovelAlterado.edificacao.numeroVagas, item)\">{{item.imovelAlterado.edificacao.numeroVagas}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.numeroVagas, item.imovelAlterado.edificacao.numeroVagas)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.numeroVagas ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Possui Elevador</td><td md-cell><span ng-show=imovelOriginal.exibir>{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiElevador)}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.possuiElevador, item.imovelAlterado.edificacao.possuiElevador, item)\">{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiElevador)}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiElevador), homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiElevador))\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.possuiElevador ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Possui Etiqueta Nacional de Conservação de Energia (ENCE) Geral</td><td md-cell><span ng-show=imovelOriginal.exibir>{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiENCE)}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.possuiENCE, item.imovelAlterado.edificacao.possuiENCE, item)\">{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiENCE)}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiENCE), homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiENCE))\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.possuiENCE ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.benfeitoriaComplementar || item.imovelAlterado.benfeitoriaComplementar\"><td md-cell>Estado de Conservação</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.estadoConservacao.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.estadoConservacao.descricao, item.imovelAlterado.estadoConservacao.descricao, item)\">{{item.imovelAlterado.estadoConservacao.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.estadoConservacao.descricao, item.imovelAlterado.estadoConservacao.descricao)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.estadoConservacaoBenfeitoria ng-true-value=null ng-false-value=null></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Tipo de Estrutura</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.tipoEstrutura.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.tipoEstrutura.descricao, item.imovelAlterado.edificacao.tipoEstrutura.descricao, item)\">{{item.imovelAlterado.edificacao.tipoEstrutura.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.tipoEstrutura.descricao, item.imovelAlterado.edificacao.tipoEstrutura.descricao)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.tipoEstrutura ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Padrão de Acabamento</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.padraoAcabamento.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.padraoAcabamento.descricao, item.imovelAlterado.edificacao.padraoAcabamento.descricao, item)\">{{item.imovelAlterado.edificacao.padraoAcabamento.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.padraoAcabamento.descricao, item.imovelAlterado.edificacao.padraoAcabamento.descricao)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.padraoAcabamento ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row><td md-cell>Observações Gerais</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.observacoes}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.observacoes, item.imovelAlterado.observacoes, item)\">{{item.imovelAlterado.observacoes}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.observacoes, item.imovelAlterado.observacoes)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.observacoes ng-true-value=true ng-false-value=false></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/detalhar-imovel-dados-benfeitoria-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-show=\"item.benfeitoriaOriginal.tipoBenfeitoria != null\"><label class=label-component>Tipo de Acessão/Benfeitoria:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.tipoBenfeitoria.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.tipoProjeto != null\"><label class=label-component>Tipo de Projeto:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.tipoProjeto.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.nome != null\"><label class=label-component>Nome:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.nome }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.areaConstruida != null\"><label class=label-component>Área Construída:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.areaConstruida }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.areaProjecao != null\"><label class=label-component>Área de Projeção:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.areaProjecao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.taxaOcupacao != null\"><label class=label-component>Taxa de Ocupação:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.taxaOcupacao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.indiceAproveitamento != null\"><label class=label-component>Índice de Aproveitamento:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.indiceAproveitamento }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.numeroPavimentos != null\"><label class=label-component>Número de Pavimentos:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.numeroPavimentos }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.numeroDormitorios != null\"><label class=label-component>Número de Domitórios:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.numeroDormitorios }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.averbada != null\"><label class=label-component>Averbada na Matrícula do Imóvel:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.averbada? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.descricaoAverbacao != null\"><label class=label-component>Descrição da Averbação:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.descricaoAverbacao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.possuiGaragem != null\"><label class=label-component>Possui Garagem:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.possuiGaragem }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.numeroVagasCobertas != null\"><label class=label-component>Número de vagas cobertas:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.numeroVagasCobertas }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.numeroVagasDescobertas != null\"><label class=label-component>Número de vagas descobertas:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.numeroVagasDescobertas }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.possuiElevador != null\"><label class=label-component>Possui Elevador:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.possuiElevador? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.possuiENCE != null\"><label class=label-component>Possui Etiqueta Nac. de Cons. de Energ. Geral:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.possuiENCE? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.tipoEnce != null\"><label class=label-component>Tipo:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.tipoEnce.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.tipoEnceParcial != null\"><label class=label-component>Tipo de Parcial:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.tipoEnceParcial.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.nivelEnce != null\"><label class=label-component>Nível:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.nivelEnce.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.observacoes != null\"><label class=label-component>Observações Gerais:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.observacoes }}</span></span></td><td md-cell><div id=benfeitoria{{$index}}><span ng-show=\"item.benfeitoriaAlterada.tipoBenfeitoria != null\"><label class=label-component>Tipo de Acessão/Benfeitoria:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.tipoBenfeitoria.descricao, item.benfeitoriaAlterada.tipoBenfeitoria.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.tipoBenfeitoria.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.tipoProjeto != null\"><label class=label-component>Tipo de Projeto:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.tipoProjeto.descricao, item.benfeitoriaAlterada.edificacao.tipoProjeto.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.tipoProjeto.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.nome != null\"><label class=label-component>Nome:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.nome, item.benfeitoriaAlterada.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.nome }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.areaConstruida != null\"><label class=label-component>Área Construída:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.areaConstruida, item.benfeitoriaAlterada.areaConstruida) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.areaConstruida }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.areaProjecao != null\"><label class=label-component>Área de Projeção:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.areaProjecao, item.benfeitoriaAlterada.edificacao.areaProjecao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.areaProjecao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.taxaOcupacao != null\"><label class=label-component>Taxa de Ocupação:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.taxaOcupacao, item.benfeitoriaAlterada.edificacao.taxaOcupacao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.taxaOcupacao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.indiceAproveitamento != null\"><label class=label-component>Índice de Aproveitamento:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.indiceAproveitamento, item.benfeitoriaAlterada.edificacao.indiceAproveitamento) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.indiceAproveitamento }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.numeroPavimentos != null\"><label class=label-component>Número de Pavimentos:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.numeroPavimentos, item.benfeitoriaAlterada.edificacao.numeroPavimentos) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.numeroPavimentos }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.numeroDormitorios != null\"><label class=label-component>Número de Domitórios:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.numeroDormitorios, item.benfeitoriaAlterada.edificacao.numeroDormitorios) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.numeroDormitorios }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.averbada != null\"><label class=label-component>Averbada na Matrícula do Imóvel:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.averbada, item.benfeitoriaAlterada.edificacao.averbada) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.averbada? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.descricaoAverbacao != null\"><label class=label-component>Descrição da Averbação:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.descricaoAverbacao, item.benfeitoriaAlterada.edificacao.descricaoAverbacao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.descricaoAverbacao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.possuiGaragem != null\"><label class=label-component>Possui Garagem:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.possuiGaragem, item.benfeitoriaAlterada.edificacao.possuiGaragem) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.possuiGaragem }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.numeroVagasCobertas != null\"><label class=label-component>Número de vagas cobertas:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.numeroVagasCobertas, item.benfeitoriaAlterada.edificacao.numeroVagasCobertas) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.numeroVagasCobertas }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.numeroVagasDescobertas != null\"><label class=label-component>Número de vagas descobertas:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.numeroVagasDescobertas, item.benfeitoriaAlterada.edificacao.numeroVagasDescobertas) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.numeroVagasDescobertas }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.possuiElevador != null\"><label class=label-component>Possui Elevador:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.possuiElevador, item.benfeitoriaAlterada.edificacao.possuiElevador) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.possuiElevador? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.possuiENCE != null\"><label class=label-component>Possui Etiqueta Nac. de Cons. de Energ. Geral:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.possuiENCE, item.benfeitoriaAlterada.edificacao.possuiENCE) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.possuiENCE? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.tipoEnce.descricao != null\"><label class=label-component>Tipo:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.tipoEnce.descricao, item.benfeitoriaAlterada.edificacao.tipoEnce.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.tipoEnce.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.tipoEnceParcial != null\"><label class=label-component>Tipo de Parcial:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.tipoEnceParcial.descricao, item.benfeitoriaAlterada.edificacao.tipoEnceParcial.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.tipoEnceParcial.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.nivelEnce != null\"><label class=label-component>Nível:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.nivelEnce.descricao, item.benfeitoriaAlterada.edificacao.nivelEnce.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.nivelEnce.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.observacoes != null\"><label class=label-component>Observações Gerais:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.observacoes, item.benfeitoriaAlterada.edificacao.observacoes) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.observacoes }}</span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar benfeitoria {{item.benfeitoriaAlterada.codigo}}\" name=benfeitoria_{{item.benfeitoriaAlterada.codigo}} ng-checked=\"homCtrl.exists(\'benfeitoria_\' + item.benfeitoriaAlterada.codigo)\" ng-disabled=\"homCtrl.isDifferent(\'#benfeitoria\'+ {{$index}})\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Condições de Acessibilidade</label><br><label class=label-component>Possui laudo de acessibilidade?</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação\" : item.benfeitoriaOriginal.edificacao.possuiLaudo.descricao}}</span><br><label class=label-component>Link do laudo de acessibilidade.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação\" : item.benfeitoriaOriginal.edificacao.linkLaudo}}</span><br><label class=label-component>Possui Plano de Trabalho?</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação\" : item.benfeitoriaOriginal.edificacao.possuiPlanoTrabalho.descricao}}</span><br><label class=label-component>Link do Plano de Trabalho</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação\" : item.benfeitoriaOriginal.edificacao.linkPlanoTrabalho}}</span><br><label class=label-component>Vagas para Pessoa com Deficiência reservadas e sinalizadas perto do acesso (2% do total de vagas).</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação\" : item.benfeitoriaOriginal.edificacao.vagasDefFisicos.descricao}}</span><br><label class=label-component>Vagas para Pessoa Idosa reservadas e sinalizadas perto do acesso (5% do total de vagas).</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.vagasIdosos.descricao}}</span><br><label class=label-component>Rebaixamento de calçadas.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.calcadasRebaixa.descricao}}</span><br><label class=label-component>Calçadas sem barreiras nas proximidades da edificação.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.calcadasLivres.descricao}}</span><br><label class=label-component>Entrada livre de barreiras (catracas, porta giratória, trilhos não embutidos, degraus, entre outros).</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.entradaLivre.descricao}}</span><br><label class=label-component>Todos os pavimentos podem ser acessados por pessoa em cadeira de rodas.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.pavimentosLivreCadRodas.descricao}}</span><br><label class=label-component>Balcão de informação acessível (com pelo menos um trecho rebaixado conforme norma técnica).</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.balcaoInformacoes.descricao}}</span><br><label class=label-component>Piso Tátil.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.pisoTatil.descricao}}</span><br><label class=label-component>Corredores com largura adequada e sem obstáculos.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.corredorLargo.descricao}}</span><br><label class=label-component>Sanitário acessível de uso público.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.sanitarioPublico.descricao}}</span><br><label class=label-component>Sinalização em braile e alto relevo junto a informações relevantes (ex. sanitários, elevadores) .</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.braile.descricao}}</span><br><label class=label-component>Atendimento em LIBRAS.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.libras.descricao}}</span></td><td md-cell><div id=benfeitoriaAcessibilidade{{$index}}><label class=label-titulo>Condições de Acessibilidade</label><br><label class=label-component>Possui laudo de acessibilidade?</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação\" : item.benfeitoriaOriginal.edificacao.possuiLaudo.descricao}}</span><br><label class=label-component>Link do laudo de acessibilidade.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação\" : item.benfeitoriaOriginal.edificacao.linkLaudo}}</span><br><label class=label-component>Possui Plano de Trabalho?</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação\" : item.benfeitoriaOriginal.edificacao.possuiPlanoTrabalho.descricao}}</span><br><label class=label-component>Link do Plano de Trabalho</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação\" : item.benfeitoriaOriginal.edificacao.linkPlanoTrabalho}}</span><br><label class=label-component>Vagas para Pessoa com Deficiência reservadas e sinalizadas perto do acesso (2% do total de vagas).</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.vagasSinalizadas.descricao, item.benfeitoriaAlterada.edificacao.vagasSinalizadas.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.vagasDefFisicos.descricao}}</span><br><label class=label-component>Vagas para Pessoa Idosa reservadas e sinalizadas perto do acesso (5% do total de vagas).</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.acessoLivreObstaculos.descricao, item.benfeitoriaAlterada.edificacao.acessoLivreObstaculos.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.vagasIdosos.descricao}}</span><br><label class=label-component>Rebaixamento de calçadas.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.banheiroAcessivel.descricao, item.benfeitoriaAlterada.edificacao.banheiroAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.calcadasRebaixa.descricao}}</span><br><label class=label-component>Calçadas sem barreiras nas proximidades da edificação.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao, item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.calcadasLivres.descricao}}</span><br><label class=label-component>Entrada livre de barreiras (catracas, porta giratória, trilhos não embutidos, degraus, entre outros).</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao, item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.entradaLivre.descricao}}</span><br><label class=label-component>Todos os pavimentos podem ser acessados por pessoa em cadeira de rodas.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao, item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.pavimentosLivreCadRodas.descricao}}</span><br><label class=label-component>Balcão de informação acessível (com pelo menos um trecho rebaixado conforme norma técnica).</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao, item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.balcaoInformacoes.descricao}}</span><br><label class=label-component>Piso Tátil.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao, item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.pisoTatil.descricao}}</span><br><label class=label-component>Corredores com largura adequada e sem obstáculos.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao, item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.corredorLargo.descricao}}</span><br><label class=label-component>Sanitário acessível de uso público.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao, item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.sanitarioPublico.descricao}}</span><br><label class=label-component>Sinalização em braile e alto relevo junto a informações relevantes (ex. sanitários, elevadores) .</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao, item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.braile.descricao}}</span><br><label class=label-component>Atendimento em LIBRAS.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao, item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.libras.descricao}}</span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Acessibilidade Benfeitoria {{item.benfeitoriaAlterada.codigo}}\" name=benfeitoriaAcessibilidade_{{item.benfeitoriaAlterada.codigo}} ng-checked=\"homCtrl.exists(\'benfeitoriaAcessibilidade_\' + item.benfeitoriaAlterada.codigo)\" ng-disabled=\"homCtrl.isDifferent(\'#benfeitoriaAcessibilidade\'+ {{$index}})\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Documentação</label><span ng-repeat=\"itemOriginal in item.benfeitoriaOriginal.edificacao.documentacao\"><br><span ng-show=\"itemOriginal.nome != null\"><label class=label-component>Documento:</label><span class=span-original-component>{{ itemOriginal.nome }}</span></span><br><span ng-show=\"itemOriginal.dataEmissao != null\"><label class=label-component>Data de Emissão:</label><span class=span-original-component>{{ itemOriginal.dataEmissao | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.dataValidade != null\"><label class=label-component>Data de Validade:</label><span class=span-original-component>{{ itemOriginal.dataValidade | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.arquivo != null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.arquivo.url }}\" download=\"{{ itemOriginal.arquivo.nomeOriginal }}\">{{ itemOriginal.arquivo.nomeOriginal }}</a></span><br></span></span></td><td md-cell><div id=benfeitoriaDocumentacao{{$index}}><label class=label-titulo>Documentação</label><span ng-repeat=\"itemAlterado in item.benfeitoriaAlterada.edificacao.documentacao\"><br><span ng-show=\"itemAlterado.nome != null\"><label class=label-component>Documento:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.documentacao[$index].nome, itemAlterado.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.nome }}</span><br></span><span ng-show=\"itemAlterado.dataEmissao  != null\"><label class=label-component>Data de Emissão:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.documentacao[$index].dataEmissao, itemAlterado.dataEmissao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataEmissao | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.dataValidade != null\"><label class=label-component>Data de Validade:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.documentacao[$index].dataValidade, itemAlterado.dataValidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataValidade | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.arquivo != null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.documentacao[$index].arquivo.nome, itemAlterado.arquivo.nome) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.arquivo.url }}\" download=\"{{ itemAlterado.arquivo.nomeOriginal }}\">{{ itemAlterado.arquivo.nomeOriginal }}</a></span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Documentação Benfeitoria {{item.benfeitoriaAlterada.codigo}}\" name=benfeitoriaDocumentacao_{{item.benfeitoriaAlterada.codigo}} ng-checked=\"homCtrl.exists(\'benfeitoriaDocumentacao_\' + item.benfeitoriaAlterada.codigo)\" ng-disabled=\"homCtrl.isDifferent(\'#benfeitoriaDocumentacao\'+ {{$index}})\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Plantas da Benfeitoria</label><span ng-repeat=\"itemOriginal in item.benfeitoriaOriginal.plantas\"><br><span ng-show=\"itemOriginal.descricao != null\"><label class=label-component>Descrição:</label><span class=span-original-component>{{ itemOriginal.descricao }}</span><br></span><span ng-show=\"itemOriginal.data != null\"><label class=label-component>Data:</label><span class=span-original-component>{{ itemOriginal.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.nomeArquivo != null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.url }}\" download=\"{{ itemOriginal.nomeArquivo }}\">{{ itemOriginal.nomeArquivo }}</a></span><br></span></span></td><td md-cell><div id=benfeitoriaPlantas{{$index}}><label class=label-titulo>Plantas da Benfeitoria</label><span ng-repeat=\"itemAlterado in item.benfeitoriaAlterada.plantas\"><br><span ng-show=\"itemAlterado.descricao != null\"><label class=label-component>Descrição:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.plantas[$index].descricao, itemAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.descricao }}</span><br></span><span ng-show=\"itemAlterado.data != null\"><label class=label-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.plantas[$index].data, itemAlterado.data) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.nomeArquivo != null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.plantas[$index].nomeArquivo, itemAlterado.nomeArquivo) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.url }}\" download=\"{{ itemAlterado.nomeArquivo }}\">{{ itemAlterado.nomeArquivo }}</a></span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Plantas Benfeitoria {{item.benfeitoriaAlterada.codigo}}\" name=benfeitoriaPlantas_{{item.benfeitoriaAlterada.codigo}} ng-checked=\"homCtrl.exists(\'benfeitoriaPlantas_\' + item.benfeitoriaAlterada.codigo)\" ng-disabled=\"homCtrl.isDifferent(\'#benfeitoriaPlantas\'+ {{$index}})\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><div style=\"margin-top: 5px\"><label class=label-titulo>Fotos da Benfeitoria</label><br><span><inc-modal-fotos titulo-modal=\"Fotos da Benfeitoria\" fotos=item.benfeitoriaOriginal.imagens></inc-modal-fotos></span></div></td><td md-cell><div style=\"margin-top: 5px\"><div id=benfeitoriaFotos{{$index}}><label class=label-titulo style=\"margin-top: 5px\">Fotos da Benfeitoria</label><br><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.imagens , item.benfeitoriaAlterada.imagens) ? \'span-alterado-component\' : \'span-original-component\'\"><inc-modal-fotos titulo-modal=\"Fotos da Benfeitoria\" fotos=item.benfeitoriaAlterada.imagens></inc-modal-fotos><span ng-hide=true ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.imagens , item.benfeitoriaAlterada.imagens) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.imagens }}</span></span></div></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Fotos Benfeitoria {{item.benfeitoriaAlterada.codigo}}\" name=benfeitoriaFotos_{{item.benfeitoriaAlterada.codigo}} ng-checked=\"homCtrl.exists(\'benfeitoriaFotos_\' + item.benfeitoriaAlterada.codigo)\" ng-disabled=\"homCtrl.isDifferent(\'#benfeitoriaFotos\'+ {{$index}})\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/detalhar-imovel-dados-benfeitoria.tmpl.html","<style>.valorCondicoesAcessibilidade{\n	width: 100px !important; padding-right:20px !important;\n}</style><div layout=column><md-table-container><table md-table flex border=2px class=tabela-mapa><thead><tr md-row><th colspan=3>Localização da Benfeitoria</th></tr><tr md-row><th class=labelMapa>Original</th><th class=labelMapa>Alterado/Novo</th><th class=labelRecusar>Recusar</th></tr></thead><tbody><tr md-row><td md-cell style=\"width: 46% !important\"><div layout=row>&nbsp;&nbsp;&nbsp;&nbsp;<div layout=column class=cellMapa><ci-mapa-incorporacao ng-if=homologarImvlCtrl.imovelOriginal showminimapa=false tabindex=\"\" imovel=homologarImvlCtrl.imovelOriginal action=detalharImovelHomologar habilitar-desenho=false></ci-mapa-incorporacao></div>&nbsp;&nbsp;</div></td></tr></tbody></table></md-table-container></div><td md-cell style=\"width: 46% !important\"><div layout=row>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<div layout=column class=cellMapa><ci-mapa-incorporacao ng-if=homologarImvlCtrl.imovelAlterado showminimapa=false tabindex=\"\" imovel=homologarImvlCtrl.imovelAlterado action=detalharImovelHomologar habilitar-desenho=false></ci-mapa-incorporacao></div></div></td><td md-cell class=checkbox-mapa><md-checkbox ng-disabled=\"imovelAlterado.id !== imovelOriginal.id && item.desabilitarCheckboxMapa\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=homologarImvlCtrl.imovelRecusado.localizacaoImovel></md-checkbox></td><tr md-row><td md-cell class=cell-fotos><span ng-show=\"imovelAlterado.id !== imovelOriginal.id\"><a href=\"\">Ver Fotos da Benfeitoria({{item.imovelOriginal.imagens.length}})</a></span></td><td md-cell class=cell-fotos><a href=\"\">Ver Fotos da Benfeitoria({{item.imovelAlterado.imagens.length}})</a></td><td md-cell class=checkbox-mapa><md-checkbox ng-disabled=true aria-label=dadosBenfeitoria ng-change=change(false) ng-model=homologarImvlCtrl.imovelRecusado.fotosBenfeitoria></md-checkbox></td></tr><div layout=column ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/plantas-da-benfeitoria.tmpl.html\'\"></div><div layout=column ng-include=\"scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/dados-acessao-benfeitoria.tmpl.html\'\"></div><div layout=column ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/condicoes-de-acessibilidade.tmpl.html\'\"></div><div layout=column ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/documentacao-benfeitoria.tmpl.html\'\"></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/documentacao-benfeitoria.tmpl.html","<md-table-container><table md-table flex border=2px class=tabela-dados><thead><tr md-row><th colspan=4>Documentação</th></tr><tr md-row ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/detalhar-imovel-label-cabecalho.tmpl.html\'\"></tr></thead><tbody><tr md-row><td md-cell>Documentos</td><td md-cell><span ng-show=imovelOriginal.exibir ng-repeat=\"planta in item.imovelOriginal.plantas\"><a href=\"\" ng-click=openPdfPlanta(planta)></a><br></span></td><td md-cell><span ng-repeat=\"planta in item.imovelAlterado.plantas\"><a href=\"\" ng-click=openPdfPlanta(planta)></a><br></span></td><td md-cell><md-checkbox ng-disabled=true ng-change=change(false) ng-model=homologarImvlCtrl.imovelRecusado.plantasBenfeitoria aria-label=dadosBenfeitoria></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/plantas-da-benfeitoria.tmpl.html","<md-table-container><table md-table flex border=2px class=tabela-dados><thead><tr md-row><th colspan=4>Plantas da Benfeitoria</th></tr><tr md-row ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/detalhar-imovel-label-cabecalho.tmpl.html\'\"></tr></thead><tbody><tr md-row><td md-cell>Plantas</td><td md-cell><span ng-show=imovelOriginal.exibir ng-repeat=\"planta in item.imovelOriginal.plantas\"><a href=\"\" ng-click=openPdfPlanta(planta)>{{planta.nomeArquivo}}</a><br></span></td><td md-cell ng-class=\"getEstiloArquivosBenfeitoria(item.imovelOriginal.plantas, item.imovelAlterado.plantas)\"><span ng-repeat=\"planta in item.imovelAlterado.plantas\"><a href=\"\" ng-click=openPdfPlanta(planta) ng-class=\"getEstiloArquivosBenfeitoria(item.imovelOriginal.plantas, item.imovelAlterado.plantas)\">{{planta.nomeArquivo}}</a><br></span></td><td md-cell><md-checkbox ng-disabled=\"!isArrayArquivosModificado(item.imovelOriginal.plantas, item.imovelAlterado.plantas)\" ng-change=change(false) ng-model=homologarImvlCtrl.imovelRecusado.plantasBenfeitoria aria-label=dadosBenfeitoria></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-dados-gerais-imovel/dados-do-proprietario-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Dados do Proprietário Titular</label><br><span ng-show=\"homCtrl.imovelOriginal.proprietario.tipoProprietarioReal !== null\"><label class=label-component>Tipo Proprietário:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.descricao === \'União\'\"><label class=label-component>CNPJ da União:</label><span class=span-original-component>00.489.828/0009-02</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietariosCartorio !== null\"><label class=label-component>Possui outro proprietário registrado em cartório:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietariosCartorio.length !== 0 ? \"Sim\" : \"Não\" }}</span><br></span><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.proprietariosCartorio\"><span ng-show=\"itemOriginal.nome !== null\"><label class=label-sub-component>Nome:</label><span class=span-original-component>{{ itemOriginal.nome }}</span><br></span><span ng-show=\"itemOriginal.cpf !== null\"><label class=label-sub-component>CPF/CNPJ:</label><span class=span-original-component>{{ itemOriginal.cpf }}</span><br></span></span><span ng-show=\"homCtrl.imovelOriginal.proprietario.orgaoEntidade !== null\"><label class=label-component>Órgão/Entidade:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietario.orgaoEntidade }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietario.codigoUg !== null\"><label class=label-component>UG Específica:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietario.codigoUg }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.cnpjProprietario !== null && homCtrl.imovelOriginal.cnpjProprietario !== \'00.489.828/0009-02\'\"><label class=label-component>CNPJ da UG:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.cnpjProprietario }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietariosTerceiros.length > 0\"><label class=label-component>Terceiros:</label><br></span><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.proprietariosTerceiros\"><span ng-show=\"itemOriginal.nome !== null\"><label class=label-sub-component>Nome:</label><span class=span-original-component>{{ itemOriginal.nome }}</span><br></span><span ng-show=\"itemOriginal.cpf !== null\"><label class=label-sub-component>CPF/CNPJ:</label><span class=span-original-component>{{ itemOriginal.cpf }}</span><br></span></span><span ng-show=\"homCtrl.imovelOriginal.proprietario.codigoOrgaoEntidadeLocatario !== null\"><label class=label-component>Órgão/Entidade Locatária:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietario.codigoOrgaoEntidadeLocatario }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietario.codigoUgLocatario !== null\"><label class=label-component>UG Específica Locatária:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietario.codigoUgLocatario }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.cnpjUgEspecificaLocatario !== null\"><label class=label-component>CNPJ da UG Locatária:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.cnpjUgEspecificaLocatario }}</span><br></span></td><td md-cell><div id=proprietarioTitular><label class=label-titulo>Dados do Proprietário Titular</label><br><span ng-show=\"homCtrl.imovelAlterado.proprietario.tipoProprietarioReal !== null\"><label class=label-component>Tipo Proprietário:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao === \'União\'\"><label class=label-component>CNPJ da União:</label><span class=span-original-component>00.489.828/0009-02</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietariosCartorio !== null\"><label class=label-component>Possui outro proprietário registrado em cartório:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietariosCartorio, homCtrl.imovelAlterado.proprietariosCartorio) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietariosCartorio.length !== 0 ? \"Sim\" : \"Não\" }}</span><br></span><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.proprietariosCartorio\"><span ng-show=\"itemAlterado.nome !== null\"><label class=label-sub-component>Nome:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietariosCartorio[$index].nome, itemAlterado.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.nome }}</span><br></span><span ng-show=\"itemAlterado.cpf !== null\"><label class=label-sub-component>CPF/CNPJ:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietariosCartorio[$index].cpf, itemAlterado.cpf) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.cpf }}</span><br></span></span><span ng-show=\"homCtrl.imovelAlterado.proprietario.orgaoEntidade !== null\"><label class=label-component>Órgão/Entidade:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.orgaoEntidade, homCtrl.imovelAlterado.proprietario.orgaoEntidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietario.orgaoEntidade }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietario.codigoUg !== null\"><label class=label-component>UG Específica:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.codigoUg, homCtrl.imovelAlterado.proprietario.codigoUg) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietario.codigoUg }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.cnpjProprietario !== null && homCtrl.imovelAlterado.cnpjProprietario !== \'00.489.828/0009-02\'\"><label class=label-component>CNPJ da UG:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.codigoUg, homCtrl.imovelAlterado.proprietario.codigoUg) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.cnpjProprietario }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietariosTerceiros.length > 0\"><label class=label-component>Terceiros:</label><br></span><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.proprietariosTerceiros\"><span ng-show=\"itemAlterado.nome !== null\"><label class=label-sub-component>Nome:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietariosTerceiros[$index].nome, itemAlterado.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.nome }}</span><br></span><span ng-show=\"itemAlterado.cpf !== null\"><label class=label-sub-component>CPF/CNPJ:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietariosTerceiros[$index].cpf, itemAlterado.cpf) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.cpf }}</span><br></span></span><span ng-show=\"homCtrl.imovelAlterado.proprietario.codigoOrgaoEntidadeLocatario !== null\"><label class=label-component>Órgão/Entidade Locatária:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.codigoOrgaoEntidadeLocatario, homCtrl.imovelAlterado.proprietario.codigoOrgaoEntidadeLocatario) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietario.codigoOrgaoEntidadeLocatario }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietario.codigoUgLocatario !== null\"><label class=label-component>UG Específica Locatária:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.codigoUgLocatario, homCtrl.imovelAlterado.proprietario.codigoUgLocatario) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietario.codigoUgLocatario }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.cnpjUgEspecificaLocatario !== null\"><label class=label-component>CNPJ da UG Locatária:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.cnpjUgEspecificaLocatario, homCtrl.imovelAlterado.cnpjUgEspecificaLocatario) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.cnpjUgEspecificaLocatario }}</span><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar dados de Incorporação\" name=proprietarioTitular ng-model=homCtrl.informacoesRecusada.proprietarioTitular ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.PROPRIETARIO_TITULAR)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior !== null || homCtrl.imovelAlterado.proprietarioAnterior !== null\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Proprietário Anterior</label><br><span ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal !== null\"><label class=label-component>Tipo Proprietário:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade !== null\"><label class=label-component>Órgão/Entidade:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior.codigoUg !== null\"><label class=label-component>UG Específica:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietarioAnterior.codigoUg }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj !== null\"><label class=label-component>Não possui CPF/CNPJ:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior.justificativa !== null\"><label class=label-component>Justificativa:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietarioAnterior.justificativa }}</span><br></span><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.proprietarioAnterior.pessoas\"><span ng-show=\"itemOriginal.nomeProprietarioReceita !== null\"><label class=label-component>Nome:</label><span class=span-original-component>{{ itemOriginal.nomeProprietarioReceita }}</span><br></span><span ng-show=\"itemOriginal.numeroDocumento !== null\"><label class=label-component>CPF/CNPJ:</label><span class=span-original-component>{{ itemOriginal.numeroDocumento }}</span><br></span></span></td><td md-cell><div id=proprietarioAnterior><label class=label-titulo>Proprietário Anterior</label><br><span ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal !== null\"><label class=label-component>Tipo Proprietário:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade !== null\"><label class=label-component>Órgão/Entidade:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade, homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.codigoUg !== null\"><label class=label-component>UG Específica:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.codigoUg, homCtrl.imovelAlterado.proprietarioAnterior.codigoUg) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.codigoUg }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj !== null\"><label class=label-component>Não possui CPF/CNPJ:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj, homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.justificativa !== null\"><label class=label-component>Justificativa:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.justificativa, homCtrl.imovelAlterado.proprietarioAnterior.justificativa) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.justificativa }}</span><br></span><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.proprietarioAnterior.pessoas\"><span ng-show=\"itemAlterado.nomeProprietarioReceita !== null\"><label class=label-component>Nome:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.pessoas[$index].nomeProprietarioReceita, homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.nomeProprietarioReceita }}</span><br></span><span ng-show=\"itemAlterado.numeroDocumento !== null\"><label class=label-component>CPF/CNPJ:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.pessoas[$index].numeroDocumento, homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.numeroDocumento }}</span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar dados de Incorporação\" name=proprietarioAnterior ng-model=homCtrl.informacoesRecusada.proprietarioAnterior ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.PROPRIETARIO_ANTERIOR)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.motivoAlteracao !== null\"><td md-cell colspan=\"{{ homCtrl.sizeColspanFull() }}\"><label class=label-titulo>Motivo da Alteração</label><br><label class=label-component>Motivo:</label><span class=span-original-component>{{ homCtrl.imovelAlterado.motivoAlteracao }}</span></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-dados-gerais-imovel/dados-do-proprietario.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados do Proprietário/Titular</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao != null\"><td md-cell>Tipo de Proprietário</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.id, homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de proprietário\" name=tipoProprietarioReal ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.id, homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.id)\" ng-checked=\"homCtrl.exists(\'tipoProprietarioReal\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietario.isIntegralmente != null\"><td md-cell>Tipo (Integral/Parcial)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietario.isIntegralmente ? \"Integral\" : \"Parcial\" }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.isIntegralmente, homCtrl.imovelAlterado.proprietario.isIntegralmente) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietario.isIntegralmente ? \"Integral\" : \"Parcial\" }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo (integral/parcial)\" name=tipoIntegralParcialProprietario ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.isIntegralmente, homCtrl.imovelAlterado.proprietario.isIntegralmente)\" ng-checked=\"homCtrl.exists(\'tipoIntegralParcialProprietario\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietario.tipoProprietarioParcial.descricao != null\"><td md-cell>Proprietário Parcial</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietario.tipoProprietarioParcial.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.tipoProprietarioParcial.id, homCtrl.imovelAlterado.proprietario.tipoProprietarioParcial.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietario.tipoProprietarioParcial.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de proprietário parcial\" name=tipoProprietarioParcial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.tipoProprietarioParcial.id, homCtrl.imovelAlterado.proprietario.tipoProprietarioParcial.id)\" ng-checked=\"homCtrl.exists(\'tipoProprietarioParcial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietario.orgaoEntidade != null\"><td md-cell>Órgão/Entidade</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietario.orgaoEntidade }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.orgaoEntidade, homCtrl.imovelAlterado.proprietario.orgaoEntidade) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietario.orgaoEntidade }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar órgão/entidade\" name=orgaoEntidadeProprietario ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.orgaoEntidade, homCtrl.imovelAlterado.proprietario.orgaoEntidade)\" ng-checked=\"homCtrl.exists(\'orgaoEntidadeProprietario\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietario.codigoUg != null\"><td md-cell>UG Específica</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietario.codigoUg }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.codigoUg, homCtrl.imovelAlterado.proprietario.codigoUg) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietario.codigoUg }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar UG específica\" name=ugEspecificaProprietario ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.codigoUg, homCtrl.imovelAlterado.proprietario.codigoUg)\" ng-checked=\"homCtrl.exists(\'ugEspecificaProprietario\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"item.nomeProprietarioReceita != null\"><td md-cell>CPF/CNPJ - Nome</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"item in homCtrl.imovelOriginal.proprietario.pessoas\">{{ item.numeroDocumento | brCpfCnpj }} - {{ item.nomeProprietarioReceita }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.pessoas, homCtrl.imovelAlterado.proprietario.pessoas) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in homCtrl.imovelAlterado.proprietario.pessoas\">{{ item.numeroDocumento | brCpfCnpj }} - {{ item.nomeProprietarioReceita }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar terceiros\" name=terceiros ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.pessoas, homCtrl.imovelAlterado.proprietario.pessoas)\" ng-checked=\"homCtrl.exists(\'terceiros\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Órgão ou entidade responsável pela gestão do imóvel</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioTitular.tipoProprietarioReal.descricao != null\"><td md-cell>Tipo de Titular</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioTitular.tipoProprietarioReal.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioTitular.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietarioTitular.tipoProprietarioReal.descricao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioTitular.tipoProprietarioReal.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de titular\" name=tipoTitular ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioTitular.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietarioTitular.tipoProprietarioReal.descricao)\" ng-checked=\"homCtrl.exists(\'tipoTitular\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioTitular.orgaoEntidade != null\"><td md-cell>Órgão/Entidade</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioTitular.orgaoEntidade }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioTitular.orgaoEntidade, homCtrl.imovelAlterado.proprietarioTitular.orgaoEntidade) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioTitular.orgaoEntidade }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar órgão/entidade do titular\" name=orgaoEntidadeTitular ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioTitular.orgaoEntidade, homCtrl.imovelAlterado.proprietarioTitular.orgaoEntidade)\" ng-checked=\"homCtrl.exists(\'orgaoEntidadeTitular\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioTitular.codigoUg != null\"><td md-cell>UG Específica</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioTitular.codigoUg }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioTitular.codigoUg, homCtrl.imovelAlterado.proprietarioTitular.codigoUg) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioTitular.codigoUg }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar UG específica do titular\" name=ugEspecificaTitular ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioTitular.codigoUg, homCtrl.imovelAlterado.proprietarioTitular.codigoUg)\" ng-checked=\"homCtrl.exists(\'ugEspecificaTitular\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioTitular.cnpjAdministracaoIndireta != null\"><td md-cell>CNPJ do Órgão</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioTitular.cnpjAdministracaoIndireta }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioTitular.cnpjAdministracaoIndireta, homCtrl.imovelAlterado.proprietarioTitular.cnpjAdministracaoIndireta) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioTitular.cnpjAdministracaoIndireta }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar CNPJ do órgão\" name=cnpjOrgaoTitular ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioTitular.cnpjAdministracaoIndireta, homCtrl.imovelAlterado.proprietarioTitular.cnpjAdministracaoIndireta)\" ng-checked=\"homCtrl.exists(\'cnpjOrgaoTitular\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Proprietário Anterior</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao != null\"><td md-cell>Tipo de Proprietário</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de proprietario anterior\" name=tipoProprietarioAnterior ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao)\" ng-checked=\"homCtrl.exists(\'tipoProprietarioAnterior\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade != null\"><td md-cell>Órgão/Entidade</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade, homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar órgão/entidade do proprietário anterior\" name=orgaoEntidadeAnterior ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade, homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade)\" ng-checked=\"homCtrl.exists(\'orgaoEntidadeAnterior\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.codigoUg != null\"><td md-cell>UG Específica</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioAnterior.codigoUg }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.codigoUg, homCtrl.imovelAlterado.proprietarioAnterior.codigoUg) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.codigoUg }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar UG específica do proprietário anterior\" name=ugEspecificaAnterior ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.codigoUg, homCtrl.imovelAlterado.proprietarioAnterior.codigoUg)\" ng-checked=\"homCtrl.exists(\'ugEspecificaAnterior\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj != null\"><td md-cell>Não possui CPF/CNPJ</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj, homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar posse CPF/CNPJ\" name=naoPossuiCpfOuCnpj ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj, homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj)\" ng-checked=\"homCtrl.exists(\'naoPossuiCpfOuCnpj\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.justificativa != null\"><td md-cell>Justificativa</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioAnterior.justificativa }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.justificativa, homCtrl.imovelAlterado.proprietarioAnterior.justificativa) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.justificativa }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar justificativa\" name=justificativaTerceiros ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.justificativa, homCtrl.imovelAlterado.proprietarioAnterior.justificativa)\" ng-checked=\"homCtrl.exists(\'justificativaTerceiros\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=homCtrl.imovelAlterado.proprietarioAnterior.pessoas><td md-cell>CPF/CNPJ - Nome</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"item in homCtrl.imovelOriginal.proprietarioAnterior.pessoas\">{{ item.numeroDocumento | brCpfCnpj }} - {{ item.nomeProprietarioReceita }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.pessoas, homCtrl.imovelAlterado.proprietarioAnterior.pessoas) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in homCtrl.imovelAlterado.proprietarioAnterior.pessoas\">{{ item.numeroDocumento | brCpfCnpj }} - {{ item.nomeProprietarioReceita }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar terceiros anterior\" name=terceirosAnterior ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.pessoas, homCtrl.imovelAlterado.proprietarioAnterior.pessoas)\" ng-checked=\"homCtrl.exists(\'terceirosAnterior\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-dados-gerais-imovel/detalhar-imovel-dados-gerais-novo.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><div style=\"margin-top: 10px; margin-bottom: 25px\"><label class=label-titulo>Detalhes do Imóvel</label><br><span ng-show=\"homCtrl.imovelOriginal.naturezaImovel !== null\"><label class=label-component>Natureza do imóvel:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.naturezaImovel.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.numeroInscricaoMunicipal !== null || homCtrl.imovelOriginal.numeroInscricaoRural !== null\"><label class=label-component>Nº de Inscrição municipal/rural:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.numeroInscricaoMunicipal ? homCtrl.imovelOriginal.numeroInscricaoMunicipal : homCtrl.imovelOriginal.numeroInscricaoRural}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.observacoesGerais !== null\"><label class=label-component>Observações Gerais:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.observacoesGerais }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.imovelOriundo !== null\"><label class=label-component>Imóvel oriundo de um sistema legado do Patrimônio:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.imovelOriundo ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.sistema !== null\"><span ng-repeat=\"sis in homCtrl.imovelOriginal.sistema\"><label class=label-component>Sistema:</label>{{ sis.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.dsSiapa !== null\"><label class=label-component>RIP SIAPA:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.dsSiapa}} </span></span><span ng-show=\"homCtrl.imovelOriginal.dsSpiunet !== null\"><label class=label-component>RIP SPIUNet:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.dsSpiunet}} </span></span><span ng-show=\"homCtrl.imovelOriginal.dsNbp !== null\"><label class=label-component>NBP:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.dsNbp}}</span></span></div></td><td md-cell><div style=\"margin-top: 10px; margin-bottom: 25px\"><div id=detalhesImovel><label class=label-titulo>Detalhes do Imóvel</label><br><span ng-show=\"homCtrl.imovelAlterado.naturezaImovel !== null\"><label class=label-component>Natureza do imóvel:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.naturezaImovel.descricao, homCtrl.imovelAlterado.naturezaImovel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.naturezaImovel.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.numeroInscricaoMunicipal !== null || homCtrl.imovelAlterado.numeroInscricaoRural !== null\"><label class=label-component>Nº de Inscrição municipal/rural:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroInscricaoMunicipal, homCtrl.imovelAlterado.numeroInscricaoMunicipal) ? \'span-alterado-component\' : \'span-original-component\'\" ng-show=homCtrl.imovelAlterado.numeroInscricaoMunicipal>{{ homCtrl.imovelAlterado.numeroInscricaoMunicipal }} </span><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroInscricaoRural, homCtrl.imovelAlterado.numeroInscricaoRural) ? \'span-alterado-component\' : \'span-original-component\'\" ng-show=homCtrl.imovelAlterado.numeroInscricaoRural>{{ homCtrl.imovelAlterado.numeroInscricaoRural }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.observacoesGerais !== null\"><label class=label-component>Observações Gerais:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.observacoesGerais, homCtrl.imovelAlterado.observacoesGerais) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.observacoesGerais}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.imovelOriundo !== null\"><label class=label-component>Imóvel oriundo de um sistema legado do Patrimônio:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.imovelOriundo, homCtrl.imovelAlterado.imovelOriundo) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.imovelOriundo ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.sistema !== null\"><!-- <label class=\"label-component\">Sistema:</label>\n                  <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.sistema.descricao,\n                                    homCtrl.imovelAlterado.sistema.descricao) ? \'span-alterado-component\' :\n                                    \'span-original-component\'\">\n                    {{ homCtrl.imovelAlterado.sistema.descricao}}\n                  </span> --> <span ng-repeat=\"sis in homCtrl.imovelAlterado.sistema\"><label class=label-component>Sistema:</label>{{ sis.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.dsSiapa !== null\"><label class=label-component>RIP SIAPA:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.dsSiapa, homCtrl.imovelAlterado.dsSiapa) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.dsSiapa }} </span></span><span ng-show=\"homCtrl.imovelAlterado.dsSpiunet !== null\"><label class=label-component>RIP SPIUNet:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.dsSpiunet, homCtrl.imovelAlterado.dsSpiunet) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.dsSpiunet}} </span></span><span ng-show=\"homCtrl.imovelAlterado.dsNbp !== null\"><label class=label-component>NBP:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.dsNbp, homCtrl.imovelAlterado.dsNbp) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.dsNbp}}</span></span></div></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar detalhes do Imóvel\" name=detalhesImovel ng-model=homCtrl.informacoesRecusada.detalhesImovel ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.DETALHES_IMOVEL)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><div style=\"margin-top: 10px; margin-bottom: 25px\"><label class=label-titulo>Dados de Incorporação</label><br><span ng-show=\"homCtrl.imovelOriginal.numeroProcesso !== null\"><label class=label-component>Nº do Processo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.numeroProcesso }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.tipoIncorporacao !== null\"><label class=label-component>Forma de Incorporação:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.tipoIncorporacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.tipoAquisicao !== null\"><label class=label-component>Tipo de Aquisição:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.tipoAquisicao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.tipoFracionamento !== null\"><label class=label-component>Tipo de Fracionamento/Parcelamento:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.tipoFracionamento.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal !== null\"><label class=label-component>Tipo de Contrato Pessoal:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.valorEncargo !== null\"><label class=label-component>Valor do Encargo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.valorEncargo }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.outroContatoPessoal !== null\"><label class=label-component>Outro Contrato Pessoal:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.outroContatoPessoal }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.isExOficio !== null\"><label class=label-component>Ex-Officio:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.isExOficio ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=false><label class=label-component>A Pedido:</label><span class=span-original-component>???</span><br></span><span ng-show=false><label class=label-component>N do Processo LPB/LMEO/Identificação de área da União:</label><span class=span-original-component>???</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.codigoOrigem !== null\"><label class=label-component>RIP de Origem:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.codigoOrigem }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.ripsFusao.length !== 0\"><label class=label-component>RIP do Imóvel Remembrado:</label><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.ripsFusao\" class=span-original-component>{{ itemOriginal.ripsFusao.rip }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.fundamento.length !== 0\"><label class=label-component>Fundamento:</label><span ng-repeat=\"item in homCtrl.imovelOriginal.incorporacao.fundamento\" class=span-original-component>{{ item.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao !== null\"><label class=label-component>Situação da Incorporação:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.direitoIncorporado !== null\"><label class=label-component>Direito Adquirido/Incorporado:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.direitoIncorporado.descricao }}</span></span></div></td><td md-cell><div style=\"margin-top: 10px; margin-bottom: 25px\"><div id=dadosIncorporacao><label class=label-titulo>Dados de Incorporação</label><br><span ng-show=\"homCtrl.imovelAlterado.numeroProcesso !== null\"><label class=label-component>Nº do Processo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroProcesso, homCtrl.imovelAlterado.numeroProcesso) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.numeroProcesso }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoIncorporacao !== null\"><label class=label-component>Forma de Incorporação:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoIncorporacao.descricao, homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.descricao ) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoAquisicao !== null\"><label class=label-component>Tipo de Aquisição:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoAquisicao.descricao, homCtrl.imovelAlterado.incorporacao.tipoAquisicao.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoAquisicao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.tipoFracionamento !== null\"><label class=label-component>Tipo de Fracionamento/Parcelamento:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.tipoFracionamento.descricao, homCtrl.imovelAlterado.tipoFracionamento.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.tipoFracionamento.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal !== null\"><label class=label-component>Tipo de Contrato Pessoal:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal.descricao, homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.valorEncargo !== null\"><label class=label-component>Valor do Encargo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.valorEncargo, homCtrl.imovelAlterado.incorporacao.valorEncargo) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.valorEncargo }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.outroContatoPessoal !== null\"><label class=label-component>Outro Contrato Pessoal:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.outroContatoPessoal, homCtrl.imovelAlterado.outroContatoPessoal) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.outroContatoPessoal }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.isExOficio !== null\"><label class=label-component>Ex-Officio:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.isExOficio, homCtrl.imovelAlterado.incorporacao.isExOficio) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.isExOficio ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=false><label class=label-component>A Pedido:</label><span ng-class=\"homCtrl.isDadoDivergente() ? \'span-alterado-component\' : \'span-original-component\'\">???</span><br></span><span ng-show=false><label class=label-component>N do Processo LPB/LMEO/Identificação de área da União:</label><span ng-class=\"homCtrl.isDadoDivergente() ? \'span-alterado-component\' : \'span-original-component\'\">???</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.codigoOrigem !== null\"><label class=label-component>RIP de Origem:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.codigoOrigem, homCtrl.imovelAlterado.incorporacao.codigoOrigem) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.codigoOrigem }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.ripsFusao.length !== 0\"><label class=label-component>RIP do Imóvel Remembrado:</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.ripsFusao\" ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.ripsFusao[$index].ripsFusao.rip, itemAlterado.ripsFusao.rip) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.ripsFusao.rip }} </span><span class=span-alterado-component ng-show=\"homCtrl.imovelOriginal.ripsFusao.length > homCtrl.imovelAlterado.ripsFusao.length\">Informação Apagada</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.fundamento.length !== 0\"><label class=label-component>Fundamento:</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.incorporacao.fundamento\" ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.fundamento[$index].descricao, itemAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.descricao }} </span><span class=span-alterado-component ng-show=\"homCtrl.imovelOriginal.incorporacao.fundamento.length > homCtrl.imovelAlterado.incorporacao.fundamento.length\">Informação Apagada</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao !== null\"><label class=label-component>Situação da Incorporação:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao.descricao, homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.direitoIncorporado !== null\"><label class=label-component>Direito Adquirido/Incorporado:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.direitoIncorporado.descricao, homCtrl.imovelAlterado.incorporacao.direitoIncorporado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.direitoIncorporado.descricao }}</span></span></div></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar dados de Incorporação\" name=dadosIncorporacao ng-model=homCtrl.informacoesRecusada.dadosIncorporacao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.DADOS_INCORPORACAO)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.titulos.length !== 0 || homCtrl.imovelAlterado.titulos.length !== 0\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><br><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.titulos\"><span ng-show=itemOriginal.nome><label class=label-titulo>Título Aquisitivo - {{ itemOriginal.nome }}</label><br></span><span ng-show=\"itemOriginal.tipoTituloAquisitivo !== null\"><label class=label-component>Tipo:</label><span class=span-original-component>{{ itemOriginal.tipoTituloAquisitivo.descricao }}</span><br></span><span ng-show=\"itemOriginal.tipoAtoLegal !== null\"><label class=label-component>Tipo de Ato Legal:</label><span class=span-original-component>{{ itemOriginal.tipoAtoLegal.descricao }}</span><br></span><span ng-show=\"itemOriginal.numero !== null\"><label class=label-component>Número:</label><span class=span-original-component>{{ itemOriginal.numero }}</span><br></span><span ng-show=\"itemOriginal.nomeArquivo !== null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.url }}\" download=\"{{ itemOriginal.nomeArquivo }}\">{{ itemOriginal.nomeArquivo }}</a></span><br></span><span ng-show=\"itemOriginal.dataAssinatura !== null\"><label class=label-component>Data da Assinatura:</label><span class=span-original-component>{{ itemOriginal.dataAssinatura | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.dataFinalVigencia !== null\"><label class=label-component>Data de final da Vigência:</label><span class=span-original-component>{{ itemOriginal.dataFinalVigencia | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.publicacao !== null\"><label class=label-component>Página:</label><span class=span-original-component>{{ itemOriginal.publicacao.pagina }}</span><br></span><span ng-show=\"itemOriginal.publicacao !== null\"><label class=label-component>Seção</label><span class=span-original-component>{{ itemOriginal.publicacao.secao }}</span><br></span><span ng-show=\"itemOriginal.publicacao !== null\"><label class=label-component>Data:</label><span class=span-original-component>{{ itemOriginal.publicacao.dataPublicacao | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.moeda !== null\"><label class=label-component>Moeda:</label><span class=span-original-component>{{ itemOriginal.moeda.descricao }}</span><br></span><span ng-show=\"itemOriginal.valorContrato !== null\"><label class=label-component>Valor do Contrato:</label><span class=span-original-component>{{ itemOriginal.valorContrato }}</span><br></span><span ng-show=\"itemOriginal.observacao !== null\"><label class=label-component>Observações/Anotações:</label><span class=span-original-component>{{ itemOriginal.observacao }}</span><br></span><span ng-show=\"itemOriginal.encargos.length !== 0\"><span ng-repeat=\"encargoOriginal in  itemOriginal.encargos\"><span ng-show=\"encargoOriginal.descricao !== null\"><label class=label-component>Encargo:</label><span class=span-original-component>{{ encargoOriginal.descricao }}</span><br></span><span ng-show=\"encargoOriginal.prazoCumprimento !== null\"><label class=label-sub-component>Prazo para cumprimento:</label><span class=span-original-component>{{ encargoOriginal.prazoCumprimento | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"encargoOriginal.cumprimento !== null\"><label class=label-sub-component>Cumprimento do Encargo:</label><span class=span-original-component>{{ encargoOriginal.cumprimento }}</span><br></span></span></span><br><br></span></td><td md-cell><div id=tituloAquisitivo><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.titulos\"><span ng-show=\"itemAlterado.nome !== null\"><label class=label-titulo>Título Aquisitivo - {{ itemAlterado.nome }}</label><br></span><span ng-show=\"itemAlterado.tipoTituloAquisitivo !== null\"><label class=label-component>Tipo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].tipoTituloAquisitivo.descricao, itemAlterado.tipoTituloAquisitivo.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.tipoTituloAquisitivo.descricao }}</span><br></span><span ng-show=\"itemAlterado.tipoAtoLegal !== null\"><label class=label-component>Tipo de Ato Legal:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].tipoAtoLegal.descricao, itemAlterado.tipoAtoLegal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.tipoAtoLegal.descricao }}</span><br></span><span ng-show=\"itemAlterado.numero !== null\"><label class=label-component>Número:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].numero, itemAlterado.numero) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.numero }}</span><br></span><span ng-show=\"itemAlterado.nomeArquivo !== null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].nomeArquivo, itemAlterado.nomeArquivo) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.url }}\" download=\"{{ itemAlterado.nomeArquivo }}\">{{ itemAlterado.nomeArquivo }}</a></span><br></span><span ng-show=\"itemAlterado.dataAssinatura !== null\"><label class=label-component>Data da Assinatura:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].tipoTituloAquisitivo.dataAssinatura, itemAlterado.tipoTituloAquisitivo.dataAssinatura) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataAssinatura | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.dataFinalVigencia !== null\"><label class=label-component>Data de final da Vigência:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].tipoTituloAquisitivo.dataFinalVigencia , itemAlterado.tipoTituloAquisitivo.dataFinalVigencia) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataFinalVigencia | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.publicacao !== null\"><label class=label-component>Página:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].publicacao.pagina , itemAlterado.publicacao.pagina) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.publicacao.pagina }}</span><br></span><span ng-show=\"itemAlterado.publicacao !== null\"><label class=label-component>Seção</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].publicacao.secao, itemAlterado.publicacao.secao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.publicacao.secao }}</span><br></span><span ng-show=\"itemAlterado.publicacao !== null\"><label class=label-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].publicacao.dataPublicacao, itemAlterado.publicacao.dataPublicacao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.publicacao.dataPublicacao | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.moeda !== null\"><label class=label-component>Moeda:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].moeda.descricao, itemAlterado.moeda.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.moeda.descricao }}</span><br></span><span ng-show=\"itemAlterado.valorContrato !== null\"><label class=label-component>Valor do Contrato:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].valorContrato, itemAlterado.valorContrato) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.valorContrato }}</span><br></span><span ng-show=\"itemAlterado.observacao !== null\"><label class=label-component>Observações/Anotações:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].observacao, itemAlterado.observacao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.observacao }}</span><br></span><span ng-show=\"itemAlterado.encargos.length !== 0\"><span ng-repeat=\"encargoAlterado in  itemAlterado.encargos\"><span ng-show=\"encargoAlterado.descricao !== null\"><label class=label-component>Encargo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$parent.$index].encargos[$index].descricao, encargoAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ encargoAlterado.descricao }}</span><br></span><span ng-show=\"encargoAlterado.prazoCumprimento !== null\"><label class=label-sub-component>Prazo para cumprimento:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$parent.$index].encargos[$index].prazoCumprimento , encargoAlterado.prazoCumprimento ) ? \'span-alterado-component\' : \'span-original-component\'\">{{ encargoAlterado.prazoCumprimento | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"encargoAlterado.cumprimento !== null\"><label class=label-sub-component>Cumprimento do Encargo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$parent.$index].encargos[$index].cumprimento, encargoAlterado.cumprimento) ? \'span-alterado-component\' : \'span-original-component\'\">{{ encargoAlterado.cumprimento }}</span><br></span></span></span><br><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Titulos Aquisitivos\" name=tituloAquisitivo ng-model=homCtrl.informacoesRecusada.tituloAquisitivo ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.MATRICULA_TRANSCRICAO)></md-checkbox><md-checkbox aria-label=\"Recusar Matricula/Transcrição\" name=matriculaTranscricao ng-model=homCtrl.informacoesRecusada.matriculaTranscricao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.MATRICULA_TRANSCRICAO)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.incorporacao.matriculas.length !== 0 || homCtrl.imovelAlterado.incorporacao.matriculas.length !== 0\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><br><span ng-show=\"homCtrl.imovelOriginal.incorporacao.matriculas.length !== 0\"><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.incorporacao.matriculas\"><span ng-show=\"itemOriginal.numero !== null\"><label class=label-titulo>Matrícula/Transcrição - {{ itemOriginal.numero }}</label><br></span><span ng-show=\"itemOriginal.uf !== null\"><label class=label-component>Município/UF:</label><span class=span-original-component>{{ itemOriginal.municipio }}/{{ itemOriginal.uf }}</span><br></span><span ng-show=\"itemOriginal.cartorioID !== null\"><label class=label-component>Nome do Cartório:</label><span class=span-original-component>{{ itemOriginal.cartorio.nome }}</span><br></span><span ng-show=\"itemOriginal.dataMatricula !== null\"><label class=label-component>Data da Matrícula:</label><span class=span-original-component>{{ itemOriginal.dataMatricula | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.dataRegistro !== null\"><label class=label-component>Data do Registro:</label><span class=span-original-component>{{ itemOriginal.dataRegistro | date: \"dd/MM/yyyy\" }}</span><br></span><br></span></span></td><td md-cell><div id=matriculaTranscricao><span ng-show=\"homCtrl.imovelAlterado.incorporacao.matriculas.length !== 0\"><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.incorporacao.matriculas\"><span ng-show=\"itemAlterado.numero !== null\"><label class=label-titulo>Matrícula/Transcrição - {{ itemAlterado.numero }}</label><br></span><span ng-show=\"itemAlterado.uf !== null\"><label class=label-component>Município/UF:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.matriculas[$index].uf , itemAlterado.uf) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.municipio }}/{{ itemAlterado.uf }}</span><br></span><span ng-show=\"itemAlterado.cartorioID !== null\"><label class=label-component>Nome do Cartório:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.matriculas[$index].cartorioID , itemAlterado.cartorioID) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.cartorio.nome }}</span><br></span><span ng-show=\"itemAlterado.dataMatricula !== null\"><label class=label-component>Data da Matrícula:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.matriculas[$index].dataMatricula , itemAlterado.dataMatricula) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataMatricula | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.dataRegistro !== null\"><label class=label-component>Data do Registro:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.matriculas[$index].dataRegistro , itemAlterado.dataRegistro) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataRegistro | date: \"dd/MM/yyyy\" }}</span><br></span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Matricula/Transcrição\" name=matriculaTranscricao ng-model=homCtrl.informacoesRecusada.matriculaTranscricao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.MATRICULA_TRANSCRICAO)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.incorporacao.certidoesCartoriais.length !== 0 || homCtrl.imovelAlterado.incorporacao.certidoesCartoriais.length !== 0\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Documentos Cartoriais</label><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.incorporacao.certidoesCartoriais \"><br><span ng-show=\"itemOriginal.descricao !== null\"><label class=label-component>Descrição:</label><span class=span-original-component>{{ itemOriginal.descricao }}</span><br></span><span ng-show=\"itemOriginal.data !== null\"><label class=label-component>Data:</label><span class=span-original-component>{{ itemOriginal.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.cartorio !== null\"><label class=label-component>Cartório:</label><span class=span-original-component>{{ itemOriginal.cartorioRecuperado.nome }}</span><br></span><span ng-show=\"itemOriginal.arquivo.nomeOriginal !== null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.arquivo.url }}\" download=\"{{ itemOriginal.arquivo.nomeOriginal }}\">{{ itemOriginal.arquivo.nomeOriginal }}</a></span><br></span></span></td><td md-cell><div id=documentoCartorial><label class=label-titulo>Documentos Cartoriais</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.incorporacao.certidoesCartoriais\"><br><span ng-show=\"itemAlterado.descricao !== null\"><label class=label-component>Descrição:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais[$index].descricao , itemAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.descricao }}</span><br></span><span ng-show=\"itemAlterado.data !== null\"><label class=label-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais[$index].data , itemAlterado.data) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.cartorio !== null\"><label class=label-component>Cartório:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais[$index].cartorio , itemAlterado.cartorio) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.cartorioRecuperado.nome }}</span><br></span><span ng-show=\"itemAlterado.arquivo.nomeOriginal !== null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais[$index].arquivo.nomeOriginal , itemAlterado.arquivo.nomeOriginal) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.arquivo.url }}\" download=\"{{ itemAlterado.arquivo.nomeOriginal }}\">{{ itemAlterado.arquivo.nomeOriginal }}</a></span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Documentos Cartoriais\" name=documentoCartorial ng-model=homCtrl.informacoesRecusada.documentoCartorial ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.DOCUMENTO_CARTORIAL)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><div style=\"margin-top: 5px\"><label class=label-titulo>Fotos do Imóvel</label><br><span><inc-modal-fotos titulo-modal=\"Fotos do Imóvel\" fotos=homCtrl.imovelOriginal.imagens></inc-modal-fotos></span></div></td><td md-cell><div style=\"margin-top: 5px\"><div id=fotosImovelGeral><label class=label-titulo style=\"margin-top: 5px\">Fotos do Imóvel</label><br><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.imagens , homCtrl.imovelAlterado.imagens) ? \'span-alterado-component\' : \'span-original-component\'\"><inc-modal-fotos titulo-modal=\"Fotos do Imóvel\" fotos=homCtrl.imovelAlterado.imagens></inc-modal-fotos><span ng-hide=true ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.imagens , homCtrl.imovelAlterado.imagens) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.imagens }}</span></span></div></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Fotos do Imóvel\" name=fotosImovelGeral ng-model=homCtrl.informacoesRecusada.fotosImovelGeral ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.FOTOS_IMOVEL_GERAL)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-dados-gerais-imovel/detalhar-imovel-dados-gerais.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Detalhes do Imóvel</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.naturezaImovel.descricao != null\"><td md-cell>Natureza do Imóvel</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.naturezaImovel.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.naturezaImovel.id, homCtrl.imovelAlterado.naturezaImovel.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.naturezaImovel.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar natureza do imóvel\" name=naturezaImovel ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.naturezaImovel.id, homCtrl.imovelAlterado.naturezaImovel.id)\" ng-checked=\"homCtrl.exists(\'naturezaImovel\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.numeroInscricaoMunicipal != null\"><td md-cell>Nº da Inscrição Municipal</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.numeroInscricaoMunicipal }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroInscricaoMunicipal, homCtrl.imovelAlterado.numeroInscricaoMunicipal) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.numeroInscricaoMunicipal }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número da inscrição municipal\" name=numeroInscricaoMunicipal ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.numeroInscricaoMunicipal, homCtrl.imovelAlterado.numeroInscricaoMunicipal)\" ng-checked=\"homCtrl.exists(\'numeroInscricaoMunicipal\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.numeroInscricaoRural != null\"><td md-cell>Nº da Inscrição Rural</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.numeroInscricaoRural }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroInscricaoRural, homCtrl.imovelAlterado.numeroInscricaoRural) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.numeroInscricaoRural }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número da inscrição rural\" name=numeroInscricaoRural ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.numeroInscricaoRural, homCtrl.imovelAlterado.numeroInscricaoRural)\" ng-checked=\"homCtrl.exists(\'numeroInscricaoRural\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.observacoesGerais != null\"><td md-cell>Observações Gerais</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.observacoesGerais }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.observacoesGerais, homCtrl.imovelAlterado.observacoesGerais) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.observacoesGerais }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar observações gerais\" name=observacoesGeraisImovel ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.observacoesGerais, homCtrl.imovelAlterado.observacoesGerais)\" ng-checked=\"homCtrl.exists(\'observacoesGeraisImovel\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados de Incorporação</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.numeroProcesso != null\"><td md-cell>Nº do Processo</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.numeroProcesso }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroProcesso, homCtrl.imovelAlterado.numeroProcesso) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.numeroProcesso }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número do processo\" name=numeroProcesso ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.numeroProcesso, homCtrl.imovelAlterado.numeroProcesso)\" ng-checked=\"homCtrl.exists(\'numeroProcesso\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.descricao != null\"><td md-cell>Forma de Incorporação</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.tipoIncorporacao.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoIncorporacao.id, homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar forma de incorporação\" name=formaIncorporacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.tipoIncorporacao.id, homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.id)\" ng-checked=\"homCtrl.exists(\'formaIncorporacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoAquisicao.descricao != null\"><td md-cell>Tipo de Aquisição</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.tipoAquisicao.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoAquisicao.id, homCtrl.imovelAlterado.incorporacao.tipoAquisicao.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoAquisicao.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de aquisição\" name=tipoAquisicao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.tipoAquisicao.id, homCtrl.imovelAlterado.incorporacao.tipoAquisicao.id)\" ng-checked=\"homCtrl.exists(\'tipoAquisicao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.tipoFracionamento.descricao != null\"><td md-cell>Tipo de Fracionamento/Parcelamento</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.tipoFracionamento.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.tipoFracionamento.id, homCtrl.imovelAlterado.tipoFracionamento.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.tipoFracionamento.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de fracionamento/parcelamento\" name=tipoFracionamento ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.tipoFracionamento.id, homCtrl.imovelAlterado.tipoFracionamento.id)\" ng-checked=\"homCtrl.exists(\'tipoFracionamento\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.descricao != null\"><td md-cell>Tipo de Contrato Pessoal</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal.id, homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de contrato pessoal\" name=tipoContratoPessoal ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal.id, homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.id)\" ng-checked=\"homCtrl.exists(\'tipoContratoPessoal\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.outroContatoPessoal != null\"><td md-cell>Descrição do Contrato/Termo</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.outroContatoPessoal }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.outroContatoPessoal, homCtrl.imovelAlterado.incorporacao.outroContatoPessoal) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.outroContatoPessoal }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar descrição do contrato/termo\" name=outroContatoPessoal ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.outroContatoPessoal, homCtrl.imovelAlterado.incorporacao.outroContatoPessoal)\" ng-checked=\"homCtrl.exists(\'outroContatoPessoal\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell colspan=4><v-accordion><v-pane ng-repeat=\"item in homCtrl.titulosAquisitivos\"><v-pane-header>Título Aquisitivo - {{ item.tituloOriginal ? item.tituloOriginal.nome : item.tituloAlterado.nome }}</v-pane-header><v-pane-content><inc-homologar-dados-titulo-aquisitivo titulos=item index=$index fn-is-cadastro-alterado=homCtrl.isCadastroAlterado() fn-desabilitar-checkbox=\"homCtrl.desabilitarCheckbox(dadoOriginal, dadoAlterado)\" fn-is-dado-divergente=\"homCtrl.isDadoDivergente(dadoOriginal, dadoAlterado)\" exists=homCtrl.exists(campo) toggle=homCtrl.toggle(element)></inc-homologar-dados-titulo-aquisitivo></v-pane-content></v-pane></v-accordion></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.codigoOrigem != null\"><td md-cell>RIP de Origem</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.codigoOrigem }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.codigoOrigem, homCtrl.imovelAlterado.incorporacao.codigoOrigem) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.codigoOrigem }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar RIP de origem\" name=codigoOrigem ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.codigoOrigem, homCtrl.imovelAlterado.incorporacao.codigoOrigem)\" ng-checked=\"homCtrl.exists(\'codigoOrigem\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"itemAlterado.ripsFusao.rip != null\"><td md-cell>RIP do Imóvel Remembrado</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.ripsFusao\">{{ itemOriginal.ripsFusao.rip }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.ripsFusao, homCtrl.imovelAlterado.ripsFusao) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.ripsFusao\">{{ itemAlterado.ripsFusao.rip }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar RIPs do imóvel remembrado\" name=ripDoImovelRemembrado ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.ripsFusao, homCtrl.imovelAlterado.ripsFusao)\" ng-checked=\"homCtrl.exists(\'ripDoImovelRemembrado\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"item.descricao != null\"><td md-cell>Fundamento</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"item in homCtrl.imovelOriginal.incorporacao.fundamento\">{{ item.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.fundamento, homCtrl.imovelAlterado.incorporacao.fundamento) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in homCtrl.imovelAlterado.incorporacao.fundamento\">{{ item.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fundamentos\" name=fundamentos ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.fundamento, homCtrl.imovelAlterado.incorporacao.fundamento)\" ng-checked=\"homCtrl.exists(\'fundamentos\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.descricao != null\"><td md-cell>Situação da Incorporação</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao.id, homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar situação da incorporação\" name=situacaoIncorporacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao.id, homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.id)\" ng-checked=\"homCtrl.exists(\'situacaoIncorporacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.direitoIncorporado.descricao != null\"><td md-cell>Direito Adquirido/Incorporado</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.direitoIncorporado.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.direitoIncorporado.id, homCtrl.imovelAlterado.incorporacao.direitoIncorporado.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.direitoIncorporado.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar direito adquirido/incorporado\" name=direitoIncorporado ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.direitoIncorporado.id, homCtrl.imovelAlterado.incorporacao.direitoIncorporado.id)\" ng-checked=\"homCtrl.exists(\'direitoIncorporado\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell colspan=4><v-accordion><v-pane ng-repeat=\"item in homCtrl.matriculas\"><v-pane-header>Matrícula/Transcrição - {{ item.matriculaOriginal ? item.matriculaOriginal.numero : item.matriculaAlterada.numero }}</v-pane-header><v-pane-content><inc-homologar-dados-matricula matriculas=item index=$index fn-is-cadastro-alterado=homCtrl.isCadastroAlterado() fn-desabilitar-checkbox=\"homCtrl.desabilitarCheckbox(dadoOriginal, dadoAlterado)\" fn-is-dado-divergente=\"homCtrl.isDadoDivergente(dadoOriginal, dadoAlterado)\" exists=homCtrl.exists(campo) toggle=homCtrl.toggle(element)></inc-homologar-dados-matricula></v-pane-content></v-pane></v-accordion></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.incorporacao.certidoesCartoriais.length > 0\"><td md-cell>Documentos Cartoriais</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"item in homCtrl.imovelOriginal.incorporacao.certidoesCartoriais\"><a ng-href=\"{{ item.arquivo.url }}\" download=\"{{ item.arquivo.nomeOriginal }}\">{{ item.arquivo.nomeOriginal }}</a></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais, homCtrl.imovelAlterado.incorporacao.certidoesCartoriais) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in homCtrl.imovelAlterado.incorporacao.certidoesCartoriais\"><a ng-href=\"{{ item.arquivo.url }}\" download=\"{{ item.arquivo.nomeOriginal }}\">{{ item.arquivo.nomeOriginal }}</a></span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar documentos cartoriais\" name=documentosCartoriais ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais, homCtrl.imovelAlterado.incorporacao.certidoesCartoriais)\" ng-checked=\"homCtrl.exists(\'documentosCartoriais\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Fotos do Imóvel</th></tr><tr md-row><th md-column width=45% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=45%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><span><inc-modal-fotos titulo-modal=\"Fotos do Imóvel\" fotos=homCtrl.imovelOriginal.imagens></inc-modal-fotos></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.imagens, homCtrl.imovelAlterado.imagens) ? \'dado-alterado\' : \'\'\"><inc-modal-fotos titulo-modal=\"Fotos do Imóvel\" fotos=homCtrl.imovelAlterado.imagens></inc-modal-fotos></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fotos do imóvel\" name=fotosImovel ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.imagens, homCtrl.imovelAlterado.imagens)\" ng-checked=\"homCtrl.exists(\'fotosImovel\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-homologar/dialog-detalhar-homologar-imoveis.html","<md-dialog aria-label=\"Localização do imóvel\" flex=60 ng-cloak id=localizacaoMapa><form name=localizacaoImovel ng-submit=incluir()><md-toolbar><div class=md-toolbar-tools><h2>Localização do imóvel</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=mapaImovelConsultar><ci-mapa-incorporacao showminimapa=false tabindex=\"\" imovel=\"\" action=detalharImoveisHomologar habilitar-desenho=false></ci-mapa-incorporacao></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/arquivos-complementares.html","<div layout=row flex=100><div layout=column flex=100><div><upload-arquivos model=imovel.arquivosComplementaresLista titulo=Documentos></upload-arquivos></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/cadastro-geometria-ua.html","<div layout=column><div layout-gt-xs=row style=\"margin-bottom: 10px\"><md-radio-group class=secaoDadosEdificacaoEditar ng-model=vm.unidadeAutonoma.tipoArquivo ng-click=vm.alterarParaArquivoUAShapeFile() style=\"float: left; margin-top: 10px\"><md-radio-button id=radio1 ng-value=1212 style=float:left>Arquivo (Shapefile)</md-radio-button><md-radio-button id=radio2 ng-value=12 style=\"float:left; margin-top: 0; margin-left: 15px !important\">Vetorização em Tela</md-radio-button></md-radio-group></div><section layout=row layout-sm=column layout-align=\"right center\" layout-wrap><md-button class=\"md-raised md-primary\" ng-click=vm.carregaArquivoShapeUA() ng-disabled=\"vm.unidadeAutonoma.tipoArquivo != 1212\">Escolher Arquivo</md-button>{{ terrenoCtrl.arquivo.nome }}<ng-md-icon ng-hide=!terrenoCtrl.arquivo.nome icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=terrenoCtrl.excluirArquivoTerrenoCadastral($event)></ng-md-icon></section><div layout=row layout-xs=column><md-input-container flex><label>Tipo de edificação</label><md-select class=secaoDadosEdificacaoEditar ng-model=imovel.unidadeAutonoma.tipoEdificacao ng-model-options=\"{trackBy : $value.id}\" required ng-class=\"imovel.unidadeAutonoma.tipoEdificacao.id !== imovelOriginal.unidadeAutonoma.tipoEdificacao.id ? \'dado-alterado\' : null\"><md-option ng-repeat=\"item in vm.tiposEdificacao\" ng-value=item ng-selected=\"item.id === imovel.unidadeAutonoma.tipoEdificacao.id\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex ng-show=!vm.isGaragem><label>Tipo de Estrutura</label><md-select class=secaoDadosEdificacaoEditar ng-model=imovel.unidadeAutonoma.tipoEstrutura ng-model-options=\"{trackBy : $value.id}\" ng-class=\"imovel.unidadeAutonoma.tipoEstrutura.id !== imovelOriginal.unidadeAutonoma.tipoEstrutura.id ? \'dado-alterado\' : null\"><md-option ng-repeat=\"item in vm.tiposEstrutura\" ng-value=item ng-selected=\"item.id === imovel.unidadeAutonoma.tipoEstrutura.id\">{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column ng-hide=\"imovel.unidadeAutonoma.tipoEdificacao.id === 4\"><md-input-container><md-checkbox class=secaoDadosEdificacaoEditar ng-model=imovel.unidadeAutonoma.possuiElevador ng-class=\"imovel.unidadeAutonoma.possuiElevador !== imovelOriginal.unidadeAutonoma.possuiElevador ? \'dado-alterado\' : null\">Possui elevador</md-checkbox></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/cadastro-geometria.html","<div layout=column><div layout-gt-xs=row style=\"margin-bottom: 10px\"><md-radio-group ng-model=imovelCtrl.benfeitoria.tipoArquivo style=\"float: left; margin-top: 10px\"><md-radio-button id=radio1 ng-value=1212 style=float:left disabled>Arquivo (Shapefile)</md-radio-button><md-radio-button id=radio2 ng-value=12 style=\"float:left; margin-top: 0; margin-left: 15px !important\" disabled>Vetorização em Tela</md-radio-button></md-radio-group></div><input ng-model=imovelCtrl.benfeitoria.file type=file style=\"margin-bottom: 25px\" disabled></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/condicoes-acessibilidade-garagem.html","<div layout=column><div layout=row layout-xs=column><md-input-container>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</md-input-container></div><div layout=row layout-xs=column><md-radio-group name=acessosLivresBarreirasArquitetonicas class=secaoCondicoesAcessibilidadeGaragemEditar ng-model=imovel.unidadeAutonoma.acessosLivresBarreirasArquitetonicas><md-radio-button id=radioAcessosLivresBarreirasArquitetonicasSim ng-value=true style=\"float: left; margin-left:20px !important\">Sim</md-radio-button><md-radio-button id=radioAcessosLivresBarreirasArquitetonicasNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0  !important\">Não</md-radio-button></md-radio-group>&nbsp;&nbsp;&nbsp;&nbsp;<md-radio-group name=semInfoAcessibilidade class=secaoCondicoesAcessibilidadeGaragemEditar ng-model=imovel.unidadeAutonoma.semInfoAcessibilidade><md-radio-button id=radioSemInfoAcessibilidade ng-value=true style=\"float: left; margin-left: 20px margin-top:0px !important\">Sem Informações de acessibilidade</md-radio-button></md-radio-group></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/condicoes-acessibilidade.html","<div layout=column><div layout=row><md-input-container><md-checkbox class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.semInfoAcessibilidade ng-click=imovelCtrl.desselecionaRadioButtons();>Sem Informações de acessibilidade</md-checkbox></md-input-container></div><div layout=row><md-input-container flex-gt-xs><label>Possui Laudo de Acessibilidade?</label><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.possuiLaudo placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=existeLaudo ng-change=atualizarLinkLaudo(); ng-disabled=condicoesAcessibilidadeDisabled() name=possuiLaudo><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex-gt-xs><label>Link do Laudo de Acessibilidade</label><input ng-disabled=false type=text ng-model=model.linkLaudo name=linkLaudo ng-required=existeLaudo maxlength=150 md-maxlength=150 aria-label=\" Link do laudo\"></md-input-container></div><div layout=row><div>Possui Plano de Trabalho?</div></div><div layout=row><md-input-container flex-gt-xs><md-select campo-ajuda=INC_FORM_BENFEITORIA_VAGAS_DEFICIENTES ng-model=model.possuiPlanoTrabalho placeholder=\"Não Informado\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=existePlano ng-change=atualizarLinkPlano(); ng-disabled=condicoesAcessibilidadeDisabled() name=possuiPlanoTrabalho><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex-gt-xs><label>Link do Plano de Trabalho</label><input ng-disabled=false type=text ng-model=model.linkPlanoTrabalho ng-required=existePlano name=linkPlano md-maxlength=150 aria-label=\" Link do plano de trabalho\"></md-input-container></div><div layout=row><md-input-container>Vagas para Pessoa com Deficiência reservadas e sinalizadas perto do acesso (2% do total de vagas).</md-input-container></div><div layout=row><md-radio-group name=vagasProximasAcessoCirculacao class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.vagasDefFisicos ng-required=\"!imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\"><md-radio-button id=radioVagasProximasDeficientesSim ng-value=true style=\"float: left; margin-left: 20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioVagasProximasDeficientesNao ng-value=false style=\"float: left; margin-left: 20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button><md-option ng-repeat=\"item in constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.vagasDefFisicos.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><md-input-container>Vagas para Pessoa Idosa reservadas e sinalizadas perto do acesso (5% do total de vagas).</md-input-container></div><div layout=row><md-radio-group name=acessosLivresBarreirasArquitetonicas class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.vagasIdosos ng-required=\"!imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\"><md-radio-button id=radioAcessosLivresBarreirasArquitetonicasSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioAcessosLivresBarreirasArquitetonicasNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0  !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.vagasIdosos.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><md-input-container>Rebaixamento de calçadas.</md-input-container></div><div layout=row><md-radio-group name=banheiroAcessivel class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.calcadasRebaixa ng-required=\"!imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\"><md-radio-button id=radioBanheiroAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioBanheiroAcessivelNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.banheiroAcessivel.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><!--</div> ng-show=\"imovel.unidadeAutonoma.possuiElevador\"> --><md-input-container>Calçadas sem barreiras nas proximidades da edificação.</md-input-container></div><div layout=row ng-show=imovel ng-show=imovel.unidadeAutonoma.><md-radio-group name=possuiElevador class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.calcadasLivres><!-- ng-show=\"imovel.unidadeAutonoma.possuiElevador\" ng-required=\"imovel.unidadeAutonoma.possuiElevador && !imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\">--><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-show=imovel.unidadeAutonoma.possuiElevador ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.calcadasLivres.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><!--</div> ng-show=\"imovel.unidadeAutonoma.possuiElevador\"> --><md-input-container>Entrada livre de barreiras (catracas, porta giratória, trilhos não embutidos, degraus, entre outros).</md-input-container></div><div layout=row ng-show=imovel ng-show=imovel.unidadeAutonoma.><md-radio-group name=possuiElevador class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.entradaLivre><!-- ng-show=\"imovel.unidadeAutonoma.possuiElevador\" ng-required=\"imovel.unidadeAutonoma.possuiElevador && !imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\">--><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.calcadasLivres.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><!--</div> ng-show=\"imovel.unidadeAutonoma.possuiElevador\"> --><md-input-container>Todos os pavimentos podem ser acessados por pessoa em cadeira de rodas.</md-input-container></div><div layout=row ng-show=imovel ng-show=imovel.unidadeAutonoma.><md-radio-group name=possuiElevador class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.pavimentosLivreCadRodas><!-- ng-show=\"imovel.unidadeAutonoma.possuiElevador\" ng-required=\"imovel.unidadeAutonoma.possuiElevador && !imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\">--><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.pavimentosLivreCadRodas.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><!--</div> ng-show=\"imovel.unidadeAutonoma.possuiElevador\"> --><md-input-container>Balcão de informação acessível (com pelo menos um trecho rebaixado conforme norma técnica).</md-input-container></div><div layout=row ng-show=imovel ng-show=imovel.unidadeAutonoma.><md-radio-group name=possuiElevador class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.balcaoInformacoes><!-- ng-show=\"imovel.unidadeAutonoma.possuiElevador\" ng-required=\"imovel.unidadeAutonoma.possuiElevador && !imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\">--><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-show=imovel.unidadeAutonoma.balcaoInformacoes ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.calcadasLivres.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><!--</div> ng-show=\"imovel.unidadeAutonoma.possuiElevador\"> --><md-input-container>Piso Tátil.</md-input-container></div><div layout=row ng-show=imovel ng-show=imovel.unidadeAutonoma.><md-radio-group name=possuiElevador class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.pisoTatil><!-- ng-show=\"imovel.unidadeAutonoma.possuiElevador\" ng-required=\"imovel.unidadeAutonoma.possuiElevador && !imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\">--><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.pisoTatil.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><!--</div> ng-show=\"imovel.unidadeAutonoma.possuiElevador\"> --><md-input-container>Corredores com largura adequada e sem obstáculos.</md-input-container></div><div layout=row ng-show=imovel ng-show=imovel.unidadeAutonoma.><md-radio-group name=calcadasLivres class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.calcadasLivres><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.corredorLargo.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div></div><div layout=row><md-input-container>Sanitário acessível de uso público.</md-input-container></div><div layout=row ng-show=imovel ng-show=imovel.unidadeAutonoma.><md-radio-group name=possuiElevador class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.sanitarioPublico><!-- ng-show=\"imovel.unidadeAutonoma.possuiElevador\" ng-required=\"imovel.unidadeAutonoma.possuiElevador && !imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\">--><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.sanitarioPublico.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><md-input-container>Sinalização em braile e alto relevo junto a informações relevantes (ex. sanitários, elevadores) .</md-input-container></div><div layout=row ng-show=imovel ng-show=imovel.unidadeAutonoma.><md-radio-group name=possuiElevador class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.braile><!-- ng-show=\"imovel.unidadeAutonoma.possuiElevador\" ng-required=\"imovel.unidadeAutonoma.possuiElevador && !imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\">--><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.braile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><!--</div> ng-show=\"imovel.unidadeAutonoma.possuiElevador\"> --><md-input-container>Atendimento em LIBRAS.</md-input-container></div><div layout=row ng-show=imovel ng-show=imovel.unidadeAutonoma.><md-radio-group name=possuiElevador class=secaoCondicoesAcessibilidadeEditar ng-model=imovel.unidadeAutonoma.libras><!-- ng-show=\"imovel.unidadeAutonoma.possuiElevador\" ng-required=\"imovel.unidadeAutonoma.possuiElevador && !imovel.unidadeAutonoma.semInfoAcessibilidade && !vm.isGaragem\">--><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=imovel.unidadeAutonoma.semInfoAcessibilidade>Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.libras.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/dados-acessao-benfeitoria.html","<div layout=column><div layout=row><md-input-container><label>Tipo Acessão/Benfeitoria</label><md-select ng-model=benfeitoriaSelecionada.tipoBenfeitoria placeholder=\"Selecione o tipo de acessão/benfeitoria\" style=\"width: 290px\" ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option ng-repeat=\"item in vm.tiposBenfeitoria | orderByBr:\'descricao\'\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><label>Especializacao Acessão/Benfeitoria</label><md-select ng-model=benfeitoriaSelecionada.tipoEspecializacaoEdificacao placeholder=\"Selecione a especialização da benfeitoria/acessão\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><md-option ng-repeat=\"item in vm.tiposEspecializacaoEdificacao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 2\"><label>Especialização Acessão/Benfeitoria</label><md-select ng-model=benfeitoriaSelecionada.tipoEspecializacaoComplementar placeholder=\"Selecione a especialização da benfeitoria/acessão\" style=\"width: 290px\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 2\"><md-option ng-repeat=\"item in vm.tiposEspecializacaoComplementar\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row><md-input-container class=md-block flex-gt-xs><label>Nome</label><input type=text ng-model=benfeitoriaSelecionada.nome></md-input-container></div><div layout=row><md-input-container class=md-block flex-gt-xs><label>Área construída(m²)</label><input type=text ng-model=benfeitoriaSelecionada.areaConstruida ui-number-mask required></md-input-container><md-input-container class=md-block flex-gt-xs ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><label>Área projeção(m²)</label><input type=text ng-model=benfeitoriaSelecionada.areaProjecao ui-number-mask ng-required=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"></md-input-container><md-input-container class=md-block flex-gt-xs md-no-float=true ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><!--alterado de taxa de ocupação para Fração de Ocupação--><label>Fração de Ocupação</label><input type=text ng-model=benfeitoriaSelecionada.taxaOcupacao ui-number-mask ng-disabled=true></md-input-container><md-input-container ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\" md-no-float><label>Índice de aproveitamento</label><input type=text ng-model=benfeitoriaSelecionada.indiceAproveitamento ng-disabled=true ui-number-mask></md-input-container></div><div layout=row ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><md-input-container><label>Número de pavimentos</label><input type=text ng-model=benfeitoriaSelecionada.numeroPavimentos ng-required=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"></md-input-container><md-input-container ng-show=\"benfeitoriaSelecionada.tipoEspecializacaoEdificacao === 1 || benfeitoriaSelecionada.tipoEspecializacaoEdificacao === 3 || benfeitoriaSelecionada.tipoEspecializacaoEdificacao === 5\" ng-required=vm.dormitorioIsRequired><label>Número de dormitórios</label><input type=text ng-model=benfeitoriaSelecionada.numeroDormitorios></md-input-container><md-input-container ng-show=\"benfeitoriaSelecionada.tipoEspecializacaoEdificacao === 3\"><label>Número de unidades residenciais</label><input type=text ng-model=benfeitoriaSelecionada.numeroUnidadesResidenciais ng-required=\"benfeitoriaSelecionada.tipoEspecializacaoEdificacao === 3\"></md-input-container></div><div layout=row ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1 && vm.matriculaIsPreenchida\"><md-input-container><md-checkbox ng-model=benfeitoriaSelecionada.averbada>Averbada na matrícula do imóvel</md-checkbox></md-input-container></div><md-input-container ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1 && benfeitoriaSelecionada.averbada\"><label>Descrição na averbação</label><input type=text ng-model=benfeitoriaSelecionada.descricaoAverbacao ng-required=benfeitoriaSelecionada.averbada></md-input-container><div layout=row ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><md-input-container style=\"margin: 5px 0 !important\"><md-checkbox ng-model=benfeitoriaSelecionada.possuiHabitese>Possui Habite-se</md-checkbox></md-input-container></div><md-input-container ng-show=benfeitoriaSelecionada.possuiHabitese ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><label>Ano Habite-se</label><input type=text ng-model=benfeitoriaSelecionada.anoHabitese ng-required=benfeitoriaSelecionada.possuiHabitese></md-input-container><div layout=row ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><md-input-container style=\"margin: 5px 0 !important\"><md-checkbox ng-model=benfeitoriaSelecionada.possuiGaragem>Possui garagem</md-checkbox></md-input-container></div><md-input-container ng-show=benfeitoriaSelecionada.possuiGaragem ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><label>Número de vagas</label><input type=text ng-model=benfeitoriaSelecionada.numeroVagas ng-required=benfeitoriaSelecionada.possuiGaragem></md-input-container><div layout=row ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1 && benfeitoriaSelecionada.numeroPavimentos >= 2\"><md-input-container style=\"margin: 5px 0 !important\"><md-checkbox ng-model=benfeitoriaSelecionada.possuiElevador>Possui elevador</md-checkbox></md-input-container></div><div layout=row ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><md-input-container style=\"margin: 5px 0 !important\"><md-checkbox ng-model=benfeitoriaSelecionada.possuiENCE>Possui Etiqueta Nacional de Conservação de Energia(ENCE) Geral</md-checkbox></md-input-container></div><div layout=row><md-input-container ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 2\"><label>Material de construção</label><md-select ng-model=benfeitoriaSelecionada.materialConstrucao placeholder=\"Selecione o material de construção\" style=\"width: 290px\" ng-required=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 2\" ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option ng-repeat=\"item in vm.materiaisConstrucao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container><label>Estado de conservação</label><md-select ng-model=benfeitoriaSelecionada.estadoConservacao placeholder=\"Selecione o estado de conservação\" style=\"width: 290px\" ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option ng-repeat=\"item in vm.estadosConservacao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><label>Tipo de estrutura</label><md-select ng-model=benfeitoriaSelecionada.tipoEstrutura placeholder=\"Selecione o tipo de estrutura\" style=\"width: 290px\" ng-required=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\" ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option ng-repeat=\"item in vm.tiposEstrutura\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row ng-show=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\"><md-input-container><label>Padrão de acabamento</label><md-select ng-model=benfeitoriaSelecionada.padraoAcabamento placeholder=\"Selecione o padrão de acabamento\" style=\"width: 290px\" ng-required=\"benfeitoriaSelecionada.tipoBenfeitoria.id === 1\" ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option ng-repeat=\"item in vm.tiposAcabamento\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><md-input-container><label>Obsrvações gerais</label><textarea ng-model=benfeitoriaSelecionada.observacoes md-maxlength=1000></textarea></md-input-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/dados-acessao-terreno.html","<div layout=column><md-input-container><label>Terreno com Acessão/Benfeitoria?</label><md-select ng-model=imovel.terreno.benfeitoria aria-label=\"Terreno com Acessão/Benfeitoria?\" placeholder=\"Selecione uma opção\" ng-required=imovelCtrl.showAcenssaoBenfeitoria ng-disabled=\"imovel.terreno.benfeitoria === \'Sim\'\"><md-option value=\"Não informado\">Não informado</md-option><md-option value=Sim>Sim</md-option><md-option value=Não>Não</md-option></md-select></md-input-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/dados-acessibilidade.html","<div layout=column><label>dados-acessibilidade2 - Há vagas próximas dos acessos de circulação de pedrestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente:</label><md-input-container><md-select ng-model=benfeitoriaSelecionada.vagasSinalizadas placeholder=\"Selecione uma opção\" style=\"width: 290px\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=true><md-option ng-repeat=\"item in vm.constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><label>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora deficiência ou com mobilidade reduzida:</label><md-input-container><md-select ng-model=benfeitoriaSelecionada.acessoLivreObstaculos placeholder=\"Selecione uma opção\" style=\"width: 290px\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=true><md-option ng-repeat=\"item in vm.constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><label>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida:</label><md-input-container><md-select ng-model=benfeitoriaSelecionada.banheiroAcessivel placeholder=\"Selecione uma opção\" style=\"width: 290px\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=true><md-option ng-repeat=\"item in vm.constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div ng-show=benfeitoriaSelecionada.possuiElevador><label>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida:</label><md-input-container><md-select ng-model=benfeitoriaSelecionada.elevadorAcessivel placeholder=\"Selecione uma opção\" style=\"width: 290px\" ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=benfeitoriaSelecionada.possuiElevador><md-option ng-repeat=\"item in vm.constantesAcessibilidade\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/dados-cadastramento.html","<input type=hidden ng-model=hasPontoImovel required><v-accordion class=vAccordion--default><v-pane expanded><v-pane-header>Dados do Cadastramento</v-pane-header><v-pane-content><div layout=column><md-input-container class=md-block flex-gt-xs><label>Número do Processo</label><input ng-model=imovel.numeroProcesso name=numeroProcesso md-maxlength=17></md-input-container><md-input-container class=md-block flex-gt-xs><label>Nome do Cadastrador</label><input ng-model=nomeCadastrador disabled></md-input-container></div></v-pane-content></v-pane></v-accordion>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/dados-descricao-cadastral.html","<div layout=column><div layout-gt-xs=row style=\"margin-bottom: 10px\"><md-radio-group ng-model=imovel.terreno.tipoDesenhoCadastral style=\"float: left; margin-top: 10px\"><md-radio-button id=radio1 ng-value=1 ng-click=terrenoCtrl.alterarParaArquivoShapeFile($event) style=float:left>Arquivo (Shapefile)</md-radio-button><md-radio-button id=radio2 ng-value=2 ng-click=terrenoCtrl.alterarParaVetorizacaoEmTela($event) style=\"float:left; margin-top: 0; margin-left: 15px !important\">Vetorização em Tela</md-radio-button></md-radio-group></div><section layout=row layout-sm=column layout-align=\"right center\" layout-wrap><md-button class=\"md-raised md-primary\" ng-click=\"terrenoCtrl.arquivo.arquivo ? terrenoCtrl.showShapeFileCadastralJaEnviado($event) : terrenoCtrl.showUploadArquivoTerrenoDialog($event)\" ng-disabled=\"imovel.terreno.tipoDesenhoCadastral === 2\">Escolher Arquivo</md-button>{{vm.imovel.terreno.arquivoCadastral.nome}}<ng-md-icon ng-hide=!imovel.terreno.arquivoCadastral.nome icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=terrenoCtrl.excluirArquivoTerrenoCadastral()></ng-md-icon></section><md-input-container class=md-block flex-gt-xs><label>Memorial Descritivo</label><textarea ng-disabled=terrenoCtrl.desabilitarPreenchimentoMemorialDescritivo class=secaoDadosDescricaoCadastralEditar ng-model=imovel.terreno.memorialDescritivoCadastral ng-required=\"imovel.terreno.geometriaTerreno && !imovel.incorporacao.numeroMatricula\" md-maxlength=2000 name=memorialDescritivoCadastral ng-class=\"imovel.terreno.memorialDescritivoCadastral !== imovelOriginal.terreno.memorialDescritivoCadastral ? \'dado-alterado\' : null\"></textarea></md-input-container><div layout=row layout-xs=column><md-input-container class=md-block flex=25><label style=\"text-align: center\">Unidade de Medida da Área:</label></md-input-container><md-input-container class=md-block flex><md-radio-group name=unidadeMedidaCadastral style=\"float: left !important\" layout=row layout-xs=column ng-model=imovel.terreno.unidadeMedidaCadastral ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral()><md-radio-button id=radioUnidadeMedidaM value=m2>m²</md-radio-button><md-radio-button id=radioUnidadeMedidaHa value=ha>ha</md-radio-button></md-radio-group></md-input-container></div><div layout-gt-xs=row style=\"height: 50px\"><md-input-container ng-class=\"forms.terrenoForm.areaTotalCadastral.$error.required && submittedTereno?\'md-input-invalid\':null\" class=md-block flex-gt-xs style=\"margin-bottom: 0px\"><label>Área Total</label><input type=text class=secaoDadosDescricaoCadastralEditar ng-model=imovel.terreno.areaTotalCadastral ng-required=imovel.terreno.geometriaTerreno name=areaTotalCadastral ui-number-mask name=areaTotalCadastral ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral(); ng-blur=\"terrenoCtrl.validaFracaoIdealImovelCadastral(); terrenoCtrl.verificarAreaTotalValidaCadastral()\" ng-class=\"imovel.terreno.areaTotalCadastral !== imovelOriginal.terreno.areaTotalCadastral ? \'dado-alterado\' : null\"> <span class=form-error ng-show=forms.terrenoForm.areaTotalCadastral.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-class=\"forms.terrenoForm.areaUniaoCadastral.$error.required && submittedTerreno?\'md-input-invalid\':null\" class=md-block flex-gt-xs ng-show=imovel.proprietario.isIntegralmente style=\"margin-bottom: 0px\"><label>Área da União</label><input type=text ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral(); class=secaoDadosDescricaoCadastralEditar ng-model=imovel.terreno.areaUniaoCadastral ui-number-mask ng-required=\"imovel.terreno.geometriaTerreno && imovel.proprietario.isIntegralmente\" ng-blur=terrenoCtrl.validaFracaoIdealImovelCadastral(); name=areaUniaoCadastral ng-class=\"imovel.terreno.areaUniaoCadastral !== imovelOriginal.terreno.areaUniaoCadastral ? \'dado-alterado\' : null\"> <span class=form-error ng-show=\"forms.terrenoForm.areaUniaoCadastral.$error.required && submittedTerreno\">Campo de preenchimento obrigatório</span></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block ng-model=text flex-gt-xs ng-show=\"imovel.terreno.unidadeMedidaCadastral == \'ha\' &&\n								imovel.terreno.areaTotalCadastral != undefined\" style=\"margin-top: 0px\">{{imovel.terreno.areaTotalCadastral*10000|number:2|numeroBr}} m²</md-input-container></div><div layout=row layout-xs=column ng-show=\"imovel.terreno.unidadeMedidaCadastral == \'ha\' && imovel.terreno.areaTotalCadastral != undefined\"><md-input-container style=\"margin-top: 0px\" class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerreno &&\n							imovel.proprietario.isIntegralmente &&\n							imovel.terreno.unidadeMedidaCadastral == \'ha\' &&\n							imovel.terreno.areaUniaoCadastral != undefined\">{{imovel.terreno.areaUniaoCadastral*10000|number:2|numeroBr}} m²</md-input-container></div><div layout-gt-xs=row><md-input-container style=\"margin-bottom: 0px\" class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4\"><label>Fração Ideal do Imóvel</label><input type=text name=fracaoIdealImovelCadastral id=fracaoIdealImovelCadastral ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral(); class=secaoDadosDescricaoCadastralEditar ng-model=\"imovel.unidadeAutonoma.fracaoIdealCadastral \" ng-required=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4\" ng-blur=terrenoCtrl.validaFracaoIdealImovelCadastral(); ui-number-mask name=fracaoIdealImovelCadastral></md-input-container><md-input-container class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4 && imovel.proprietario.isIntegralmente\"><label style=\"margin-left: 90px !important\">Fração Ideal da União(m²)</label></md-input-container><md-input-container class=md-block flex-xs ng-show=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4 && imovel.proprietario.isIntegralmente\" style=\"margin-right: 300px !important\"><label>{{imovel.terreno.fracaoIdealUniaoCadastral}}</label></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs ng-show=\"\n							imovel.terreno.geometriaTerreno &&\n							imovel.tipoImovel.id === 4 &&\n							imovel.terreno.unidadeMedidaCadastral == \'ha\' &&\n							imovel.unidadeAutonoma.fracaoIdealCadastral  != undefined\" style=\"margin-top: 0px\">{{imovel.unidadeAutonoma.fracaoIdealCadastral *10000}} m²</md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/dados-descricao-cartorial.html","<div layout=column><div layout-gt-xs=row style=\"margin-bottom: 10px\"><md-radio-group class=secaoDescricaoCartorial ng-model=terrenoCtrl.terreno.tipoDesenhoCartorial style=\"float: left; margin-top: 10px\"><md-radio-button id=radio1 ng-value=222 ng-click=terrenoCtrl.alterarParaArquivoShapeFileCartorial($event) style=float:left>Arquivo (Shapefile)</md-radio-button><md-radio-button id=radio2 ng-value=333 ng-click=\"terrenoCtrl.alterarParaDescricaoNaoGeorreferenciada($event); imovelCtrl.limparMemorial()\" style=\"float:left; margin-top: 0; margin-left: 15px !important\">Descrição Não Georreferenciada</md-radio-button></md-radio-group></div><section layout=row layout-sm=column layout-align=\"right center\" layout-wrap><md-button id=btnEscolherArquivo class=\"md-raised md-primary\" ng-click=\"terrenoCtrl.arquivoCartorial.arquivo ? terrenoCtrl.showShapeFileCartorialJaEnviado($event) : terrenoCtrl.showUploadArquivoTerrenoDialogCartorial($event)\">Escolher Arquivo</md-button>{{imovel.terreno.arquivoCartorial.nome}}<ng-md-icon ng-hide=!imovel.terreno.arquivoCartorial.nome icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=terrenoCtrl.excluirArquivoTerrenoCadastral(terrenoCtrl.arquivoCartorial)></ng-md-icon></section><md-checkbox class=secaoDadosDescricaoCartorialEditar ng-model=imovel.terreno.isCertificaoIncra ng-show=vm.showCertificadoIncra ng-class=\"imovel.terreno.isCertificaoIncra !== imovelOriginal.terreno.isCertificaoIncra ? \'dado-alterado\' : null\">Levantamento cadastral certificado pelo Incra</md-checkbox><md-input-container class=md-block flex-gt-xs><label>Memorial Descritivo</label><textarea class=secaoDadosDescricaoCartorialEditar ng-model=imovel.terreno.memorialDescritivoCartorial ng-required=\"imovel.terreno.geometriaTerrenoCartorial && imovel.tipoImovel.id !== 5 && imovel.tipoImovel.tipoPai !== \'UNIDADE AUTÔNOMA\'\" md-maxlength=2000 name=memorialDescritivoCartorial ng-class=\"imovel.terreno.memorialDescritivoCartorial !== imovelOriginal.terreno.memorialDescritivoCartorial ? \'dado-alterado\' : null\"></textarea><span class=form-error ng-show=forms.terrenoForm.memorialDescritivoCartorial.$error.required>Campo de preenchimento obrigatório</span></md-input-container><div layout=row layout-xs=column ng-show=imovel.terreno.geometriaTerrenoCartorial><md-input-container class=md-block flex=25><label style=\"text-align: center\">Unidade de Medida da Área:</label></md-input-container><md-input-container class=md-block flex><md-radio-group name=unidadeMedidaCartorial style=\"float: left !important\" layout=row layout-xs=column ng-model=imovel.terreno.unidadeMedidaCartorial ng-change=terrenoCtrl.calcularFracaoIdealUniaoCartorial()><md-radio-button id=radioUnidadeMedidaM value=m2>m²</md-radio-button><md-radio-button id=radioUnidadeMedidaHa value=ha>ha</md-radio-button></md-radio-group></md-input-container></div><div layout-gt-xs=row style=\"height: 50px\"><md-input-container class=md-block flex-gt-xs ng-show=imovel.terreno.geometriaTerrenoCartorial style=\"margin-bottom: 0px\"><label>Área Total</label><input type=text class=secaoDadosDescricaoCartorialEditar ng-model=imovel.terreno.areaTotalCartorial ng-required=imovel.terreno.geometriaTerrenoCartorial ui-number-mask name=areaTotalCartorial ng-class=\"imovel.terreno.areaTotalCartorial !== imovelOriginal.terreno.areaTotalCartorial ? \'dado-alterado\' : null\" ng-blur=terrenoCtrl.verificarAreaTotalValidaCartorial();> <span class=form-error ng-show=forms.terrenoForm.areaTotalCartorial.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-input-container class=md-block flex-gt-xs ng-show=imovel.proprietario.isIntegralmente style=\"margin-bottom: 0px\"><label>Área da União</label><input type=text class=secaoDadosDescricaoCartorialEditar ng-model=imovel.terreno.areaUniaoCartorial ng-required=\"imovel.terreno.geometriaTerrenoCartorial && imovel.proprietario.isIntegralmente\" ui-number-mask name=areaUniaoCartorial ng-class=\"imovel.terreno.areaUniaoCartorial !== imovelOriginal.terreno.areaUniaoCartorial ? \'dado-alterado\' : null\"> <span class=form-error ng-show=forms.terrenoForm.areaUnicaoCartorial.$error.required>Campo de preenchimento obrigatório</span></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerrenoCartorial &&\n									imovel.terreno.unidadeMedidaCartorial == \'ha\' &&\n              						imovel.terreno.areaTotalCartorial != undefined\" style=\"margin-top: 0px\">{{imovel.terreno.areaTotalCartorial*10000}} m²</md-input-container><md-input-container class=md-block flex-gt-xs ng-show=\"imovel.terreno.unidadeMedidaCartorial == \'ha\' &&\n              						imovel.terreno.areaUniaoCartorial != undefined\" style=\"margin-top: 0px\">{{imovel.terreno.areaUniaoCartorial*10000}} m²</md-input-container></div><!--\n	<div layout-gt-xs=\"row\">\n    <md-input-container class=\"md-block\" flex-gt-xs ng-show=\"imovel.terreno.geometriaTerrenoCartorial && imovel.tipoImovel.id === 4\">\n      <label>Fração Ideal do Imóvel(m²)</label>\n      <input type=\"text\"\n             class=\"secaoDadosDescricaoCartorialEditar\"\n             ng-model=\"imovel.unidadeAutonoma.fracaoIdealCartorial\"\n             ng-required=\"imovel.terreno.geometriaTerrenoCartorial && imovel.tipoImovel.id === 4\"\n             ui-number-mask\n             name=\"fracaoIdealImovelCartorial\"\n             id=\"fracaoIdealImovelCartorial\"\n             ng-class=\"imovel.unidadeAutonoma.fracaoIdealCartorial !== imovelOriginal.unidadeAutonoma.fracaoIdealCartorial ? \'dado-alterado\' : null\">\n             >\n\n        <span class=\"form-error\"\n              ng-show=\"forms.terrenoForm.fracaoIdealImovelCartorial.$error.required && submittedTerreno\">Campo de preenchimento obrigatório</span>\n    </md-input-container>\n    <md-input-container class=\"md-block\" flex-gt-xs ng-show=\"imovel.terreno.geometriaTerrenoCartorial && imovel.tipoImovel.id === 4 && imovel.proprietario.isIntegralmente\">\n      <label>Fração Ideal da União(m²)</label>\n      <input type=\"text\"\n             class=\"secaoDadosDescricaoCartorialEditar\"\n             ng-model=\"imovel.terreno.fracaoIdealUniaoCartorial\"\n             ng-required=\"imovel.terreno.geometriaTerrenoCartorial && imovel.tipoImovel.id === 4 && imovel.proprietario.isIntegralmente\"\n             ui-number-mask\n             name=\"fracaoIdealUniaoCartorial\"\n             ng-class=\"imovel.terreno.fracaoIdealUniaoCartorial !== imovelOriginal.terreno.fracaoIdealUniaoCartorial ? \'dado-alterado\' : null\">\n        <span class=\"form-error\"\n              ng-show=\"forms.terrenoForm.fracaoIdealUniaoCartorial.$error.required && submittedTerreno\">Campo de preenchimento obrigatório</span>\n    </md-input-container>\n  </div>--></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/dados-garagem.html","<div layout=column><div layout=row><md-input-container flex><label>Área</label><input ng-model=imovel.unidadeAutonoma.areaGaragem ui-number-mask required ng-class=\"imovel.unidadeAutonoma.areaGaragem !== imovelOriginal.unidadeAutonoma.areaGaragem ? \'dado-alterado\' : null\"></md-input-container><md-input-container flex><label>Tipo de garagem</label><md-select ng-model=imovel.unidadeAutonoma.tipoGaragem ng-model-options=\"{trackBy : $value.id}\" ng-required ng-class=\"imovel.unidadeAutonoma.tipoGaragem !== imovelOriginal.unidadeAutonoma.tipoGaragem ? \'dado-alterado\' : null\"><md-option ng-value=1>Coberta</md-option><md-option ng-value=2>Descoberta</md-option></md-select></md-input-container></div><div layout=row><md-input-container class=md-block flex><label>Memorial Descritivo</label><textarea ng-model=imovel.unidadeAutonoma.memorialDescritivoGaragem md-maxlength=2000 ng-class=\"imovel.unidadeAutonoma.memorialDescritivoGaragem !== imovelOriginal.unidadeAutonoma.memorialDescritivoGaragem ? \'dado-alterado\' : null\"></textarea></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/dados-unidade-autonoma.html","<div layout=column><div layout=row layout-xs=column><md-input-container><label>Área construída</label><input class=secaoDadosUAEditar ng-model=imovel.unidadeAutonoma.areaConstruida ui-number-mask required name=areaConstruida ng-class=\"imovel.unidadeAutonoma.areaConstruida !== imovelOriginal.unidadeAutonoma.areaConstruida ? \'dado-alterado\' : null\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs><label>Memorial Descritivo</label><textarea class=secaoDadosUAEditar ng-model=imovel.unidadeAutonoma.memorialDescritivo md-maxlength=2000 ng-class=\"imovel.unidadeAutonoma.memorialDescritivo !== imovelOriginal.unidadeAutonoma.memorialDescritivo ? \'dado-alterado\' : null\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex ng-show=\"imovel.tipoUnidadeAutonoma.id === 1\"><label>Número de dormitórios</label><input class=secaoDadosUAEditar ng-model=imovel.unidadeAutonoma.numeroDormitorios type=number ng-class=\"imovel.unidadeAutonoma.numeroDormitorios !== imovelOriginal.unidadeAutonoma.numeroDormitorios ? \'dado-alterado\' : null\"></md-input-container><md-input-container flex><label>Padrão de acabamento</label><md-select class=secaoDadosUAEditar ng-model=imovel.unidadeAutonoma.padraoAcabamento ng-model-options=\"{trackBy : $value.id}\" name=padraoAcabamento required ng-class=\"imovel.unidadeAutonoma.padraoAcabamento !== imovelOriginal.unidadeAutonoma.padraoAcabamento ? \'dado-alterado\' : null\"><md-option ng-repeat=\"item in vm.tiposAcabamento\" ng-value=item ng-selected=\"item.id === imovel.unidadeAutonoma.padraoAcabamento.id\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex><label>Estado de conservação</label><md-select class=secaoDadosUAEditar ng-model=imovel.unidadeAutonoma.estadoConservacao ng-model-options=\"{trackBy : $value.id}\" name=estadoConservacao required ng-class=\"imovel.unidadeAutonoma.estadoConservacao.id !== imovelOriginal.unidadeAutonoma.estadoConservacao.id ? \'dado-alterado\' : null\"><md-option ng-repeat=\"item in vm.estadosConservacao\" ng-value=item ng-selected=\"item.id === imovel.unidadeAutonoma.estadoConservacao.id\">{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container style=\"margin-right: 25px\"><md-checkbox class=secaoDadosUAEditar ng-model=imovel.unidadeAutonoma.possuiGaragem ng-class=\"imovel.unidadeAutonoma.possuiGaragem !== imovelOriginal.unidadeAutonoma.possuiGaragem ? \'dado-alterado\' : null\">Possui garagem</md-checkbox></md-input-container><md-input-container ng-show=imovel.unidadeAutonoma.possuiGaragem ng-required=imovel.unidadeAutonoma.possuiGaragem><md-checkbox name=garagemMatricula class=secaoDadosUAEditar ng-model=imovel.unidadeAutonoma.garagemComMatricula ng-class=\"imovel.unidadeAutonoma.garagemComMatricula !== imovelOriginal.unidadeAutonoma.garagemComMatricula ? \'dado-alterado\' : null\">Garagem com matrícula</md-checkbox></md-input-container></div><div layout=row ng-show=imovel.unidadeAutonoma.possuiGaragem><md-input-container flex ng-required=imovel.unidadeAutonoma.possuiGaragem><label>Tipo de garagem</label><md-select class=secaoDadosUAEditar ng-model=imovel.unidadeAutonoma.tipoGaragemUA ng-model-options=\"{trackBy : $value.id}\" name=tipoGaragemUA ng-required ng-class=\"imovel.unidadeAutonoma.tipoGaragemUA !== imovelOriginal.unidadeAutonoma.tipoGaragemUA ? \'dado-alterado\' : null\"><md-option ng-value=1 ng-selected=\"imovel.unidadeAutonoma.tipoGaragemUA === 1\">Coberta</md-option><md-option ng-value=2 ng-selected=\"imovel.unidadeAutonoma.tipoGaragemUA === 2\">Descoberta</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs ng-required=imovel.unidadeAutonoma.possuiGaragem><label>Quantidade de vagas</label><input name=numeroVagas class=secaoDadosUAEditar ng-model=imovel.unidadeAutonoma.numeroVagas type=number ng-class=\"imovel.unidadeAutonoma.numeroVagas !== imovelOriginal.unidadeAutonoma.numeroVagas ? \'dado-alterado\' : null\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs><label>Observações gerais</label><textarea class=secaoDadosUAEditar ng-model=imovel.unidadeAutonoma.observacoes md-maxlength=2000 ng-class=\"imovel.unidadeAutonoma.observacoes !== imovelOriginal.unidadeAutonoma.observacoes ? \'dado-alterado\' : null\"></textarea></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/dialog-upload-planta-unidade.html","<md-dialog aria-label=\"Upload de Planta da Unidade Autônoma\" flex=60 ng-cloak><form name=uploadPlanta><md-toolbar><div class=md-toolbar-tools><h2>Upload de Planta da Unidade Autônoma</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout-gt-sm=row layout-margin><md-input-container class=md-block flex-gt-sm><label>Nome</label><input ng-model=plantaUnidade.nome type=text required></md-input-container><md-datepicker ng-mask=39/19/9999 restrict=reject ng-model=plantaUnidade.dataEmissao md-placeholder=\"Data de emissão\" md-max-date=hoje required></md-datepicker></div><div layout-gt-sm=row layout-margin><input type=file ngf-select ng-model=plantaUnidade.planta accept=\".pdf, .png, .jpg\" ngf-max-size=10MB ngf-model-invalid=errorFile required><md-button layout-align=right class=\"md-primary md-raised\" ng-click=incluir()>Inserir</md-button></div><div layout=row ng-show=\"plantasUnidade.length > 0\"><md-content style=\"width: 100% !important\"><h4>Plantas da unidade autônoma</h4><table class=mui-table style=\"width:100% !important\"><thead><tr><th style=\"width: 200px !important\">Nome da certidão</th><th>Data da emissão</th><th>Nome do arquivo</th><th>Ação</th></tr></thead><tbody><tr ng-repeat=\"item in plantasUnidade\" ng-model-options=\"{trackBy: \'$value.id\'}\"><td>{{item.nome}}</td><td>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td>{{item.nomeArquivo}}</td><td><ng-md-icon icon=search style=fill:#788489 size=18 alt=\"Visualizar documento\" ng-click=visualizarPlanta(item.id)></ng-md-icon><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=removerPlanta($index)></ng-md-icon></td></tr></tbody></table></md-content></div><div layout-gt-sm=row layout-margin><md-button layout-align=right class=md-primary ng-click=confirmar()>Confirmar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/documentacao-unidade-autonoma.html","<div layout=column><div layout=row><md-input-container><md-checkbox class=secaoDadosDocumentacaoEditar ng-model=vm.imovel.unidadeAutonoma.possuiPlanoProtecaoIncendio aria-label=\"Plano de proteção contra incêndio\">Plano de proteção contra incêndio</md-checkbox></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacaoEditar\" ngf-select ng-model=vm.imovel.unidadeAutonoma.planoProtecaoIncendio style=\"margin: -4px 0 0 15px\" ng-change=\"file2blob(\'planoProtecaoIncendioFile\', vm.imovel.unidadeAutonoma.planoProtecaoIncendio)\" type=file ng-disabled=!vm.imovel.unidadeAutonoma.possuiPlanoProtecaoIncendio accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-required=\"vm.imovel.unidadeAutonoma.possuiPlanoProtecaoIncendio && vm.imovel.unidadeAutonoma.planoProtecaoIncendio === undefined\" name=planoProtecaoIncendioFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.planoProtecaoIncendioFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"vm.imovel.unidadeAutonoma.planoProtecaoIncendio != undefined\"><ng-md-icon ng-click=\"vm.imovel.unidadeAutonoma.planoProtecaoIncendio = undefined\" class=icon-delete icon=delete></ng-md-icon></md-input-container></div><div layout=row><md-input-container><md-checkbox class=secaoDadosDocumentacaoEditar ng-model=vm.imovel.unidadeAutonoma.possuiHabitese aria-label=Habite-se>Habite-se</md-checkbox></md-input-container><md-input-container ng-show=imovel.unidadeAutonoma.possuiHabitese style=\"margin: 12px 0 0 20px\"><label>Ano:</label><input class=secaoDadosDocumentacaoEditar ng-model=vm.imovel.unidadeAutonoma.anoHabitese type=number maxlength=4></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacaoEditar\" ngf-select ng-model=vm.imovel.unidadeAutonoma.habitese type=file style=\"margin: -4px 0 0 15px\" ng-change=\"file2blob(\'habiteseFile\', vm.imovel.unidadeAutonoma.habitese)\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-disabled=!vm.imovel.unidadeAutonoma.possuiHabitese ng-required=\"vm.imovel.unidadeAutonoma.possuiHabitese && vm.imovel.unidadeAutonoma.habitese === undefined\" name=habiteseFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.habiteseFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span><md-input-container><md-input-container ng-show=\"vm.imovel.unidadeAutonoma.habitese != undefined\"><ng-md-icon ng-click=\"vm.imovel.unidadeAutonoma.habitese = undefined\" class=icon-delete icon=delete></ng-md-icon></md-input-container></md-input-container></md-input-container></div><div layout=row><md-input-container><md-checkbox class=secaoDadosDocumentacaoEditar ng-model=vm.imovel.unidadeAutonoma.possuiAlvaraConstrucao aria-label=\"Alvará de construção/reforma\">Alvará de construção/reforma</md-checkbox></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacaoEditar\" ngf-select ng-model=vm.imovel.unidadeAutonoma.alvaraConstrucao ng-change=\"file2blob(\'alvaraConstrucaoFile\', vm.imovel.unidadeAutonoma.alvaraConstrucao)\" type=file style=\"margin: -4px 0 0 15px\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-disabled=!vm.imovel.unidadeAutonoma.possuiAlvaraConstrucao ng-required=\"vm.imovel.unidadeAutonoma.possuiAlvaraConstrucao && vm.imovel.unidadeAutonoma.alvaraConstrucao === undefined\" name=alvaraConstrucaoFile> <span class=form-error ng-show=adicionarUnidadeAutonomaForm.alvaraConstrucaoFile.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"vm.imovel.unidadeAutonoma.alvaraConstrucao != undefined\"><ng-md-icon ng-click=\"vm.imovel.unidadeAutonoma.alvaraConstrucao = undefined\" class=icon-delete icon=delete></ng-md-icon></md-input-container></div><div layout=row><md-input-container><md-checkbox class=secaoDadosDocumentacaoEditar ng-model=vm.imovel.unidadeAutonoma.possuiLaudoPredial aria-label=\"Laudo de inspeção predial\">Laudo de inspeção predial</md-checkbox></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacaoEditar\" ngf-select ng-model=vm.imovel.unidadeAutonoma.laudoPredial ng-change=\"file2blob(\'laudoPredialFile\', vm.imovel.unidadeAutonoma.laudoPredial)\" type=file accept=.pdf,.img,.jpg,.jpeg style=\"margin: -4px 0 0 15px\" ngf-max-size=10MB ng-disabled=!vm.imovel.unidadeAutonoma.possuiLaudoPredial ng-required=\"vm.imovel.unidadeAutonoma.possuiLaudoPredial && vm.imovel.unidadeAutonoma.laudoPredial === undefined\" name=laudoPredialFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.laudoPredialFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span><md-input-container><md-input-container ng-show=\"vm.imovel.unidadeAutonoma.laudoPredial != undefined\"><ng-md-icon ng-click=\"vm.imovel.unidadeAutonoma.laudoPredial = undefined\" class=icon-delete icon=delete></ng-md-icon></md-input-container></md-input-container></md-input-container></div><div layout-gt-xs=row><md-input-container><label>Outro:</label><textarea class=secaoDadosDocumentacaoEditar ng-model=vm.imovel.unidadeAutonoma.outroDescricao>\n		</md-input-container>\n\n		<md-input-container>\n			<input class=\"md-raised secaoDadosDocumentacaoEditar\" style=\"margin: -4px 0 0 15px\" ngf-select ng-model=vm.imovel.unidadeAutonoma.outro ng-change=\"file2blob(\'outroFile\', vm.imovel.unidadeAutonoma.outro)\" type=file ng-disabled=\"vm.imovel.unidadeAutonoma.outroDescricao === undefined\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-required=\"vm.imovel.unidadeAutonoma.outroDescricao !== undefined && vm.imovel.unidadeAutonoma.outro === undefined\" name=outroDescricaoFile>\n			<span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.outroDescricaoFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span>\n		</md-input-container>\n\n		<md-input-container ng-show=\"vm.imovel.unidadeAutonoma.outro != undefined\">\n			<ng-md-icon ng-click=\"vm.imovel.unidadeAutonoma.outro = undefined\" class=icon-delete icon=delete></ng-md-icon>\n		</md-input-container>\n	</div>\n\n</div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/editar-imovel/componentes/planta-unidade.html","<div layout=column><div layout=row><md-button class=\"md-raised md-primary\" ng-click=\"vm.resetarPlantaUnidade(); vm.showUploadPlantaUnidadeDialog($event)\" aria-label=\"upload planta unidade\">Adicionar plantas da unidade</md-button></div><div layout=row ng-show=\"vm.imovel.unidadeAutonoma.plantasUnidade.length > 0\"><md-content style=\"width: 100% !important\"><h4>Plantas da unidade autônoma</h4><table class=mui-table style=\"width:100% !important\"><thead><tr><th style=\"width: 200px !important\">Nome da certidão</th><th>Data da emissão</th><th>Nome do arquivo</th><th>Ação</th></tr></thead><tbody><tr ng-repeat=\"item in vm.imovel.unidadeAutonoma.plantasUnidade\" ng-model-options=\"{trackBy: \'$value.id\'}\"><td>{{item.nome}}</td><td>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td>{{item.nomeArquivo}}</td><td><ng-md-icon icon=search style=fill:#788489 size=18 alt=\"Visualizar documento\" ng-click=vm.visualizarPlanta(item.id)></ng-md-icon><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=vm.removerPlanta($index)></ng-md-icon></td></tr></tbody></table></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-unidade-autonoma/dados-da-edificacao.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Geometria da Edificação</th></tr><tr md-row><th md-column width=45%>Original</th><th md-column width=45%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell><inc-mapa-homologar ng-show=homCtrl.isCadastroAlterado() geometria=homCtrl.imovelOriginal.unidadeAutonoma.edificacao.geometria></inc-mapa-homologar></td><td md-cell><inc-mapa-homologar geometria=homCtrl.imovelAlterado.unidadeAutonoma.edificacao.geometria dado-alterado=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.geometria.coordinates, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.geometria.coordinates)\"></inc-mapa-homologar></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar geometria da edificação\" name=geometriaEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.geometria.coordinates, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.geometria.coordinates)\" ng-checked=\"homCtrl.exists(\'geometriaEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados da Edificação</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Tipo de Edificação</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao.id, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo da edificação\" name=tipoEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao.id, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao.id)\" ng-checked=\"homCtrl.exists(\'tipoEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Possui Elevador</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.boolToString(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador) }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador) ? \'dado-alterado\' : \'\'\">{{ homCtrl.boolToString(homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador) }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar possui elevador\" name=edificacaoPossuiElevador ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador)\" ng-checked=\"homCtrl.exists(\'edificacaoPossuiElevador\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Tipo de Projeto</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto.id, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de projeto\" name=tipoProjetoEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto.id, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto.id)\" ng-checked=\"homCtrl.exists(\'tipoProjetoEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Nome</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar nome da edificacao\" name=nomeEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome)\" ng-checked=\"homCtrl.exists(\'nomeEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Observações Gerais da Edificação</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar observações da edificação\" name=observacoesEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes)\" ng-checked=\"homCtrl.exists(\'observacoesEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Condições de Acessibilidade</th></tr><tr md-row><th md-column width=50%>Campo</th><th md-column width=20%>Original</th><th md-column width=20%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Há vagas próximas dos acessos de circulação de pedrestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (vagas)\" name=condicoesAcessibilidadeVagasEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga)\" ng-checked=\"homCtrl.exists(\'condicoesAcessibilidadeVagasEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (mobilidade)\" name=condicoesAcessibilidadeMobilidadeEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade)\" ng-checked=\"homCtrl.exists(\'condicoesAcessibilidadeMobilidadeEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (banheiro)\" name=condicoesAcessibilidadeBanheiroEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro)\" ng-checked=\"homCtrl.exists(\'condicoesAcessibilidadeBanheiroEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (elevador)\" name=condicoesAcessibilidadeElevadorEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador)\" ng-checked=\"homCtrl.exists(\'condicoesAcessibilidadeElevadorEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Documentação</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Documentos</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado() ng-repeat=\"item in homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao\"><a ng-href=\"{{ item.arquivo.url }}\" download=\"{{ item.arquivo.nomeOriginal }}\">{{ item.arquivo.nomeOriginal }}</a></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.documentacao) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in homCtrl.imovelAlterado.unidadeAutonoma.edificacao.documentacao\"><a ng-href=\"{{ item.arquivo.url }}\" download=\"{{ item.arquivo.nomeOriginal }}\">{{ item.arquivo.nomeOriginal }}</a></span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar documentação da edificação\" name=documentacaoEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.documentacao)\" ng-checked=\"homCtrl.exists(\'documentacaoEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-unidade-autonoma/detalhar-imovel-dados-edificacao-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao !== null\"><label class=label-component>Tipo de Edificação:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador !== null\"><label class=label-component>Possui Elevador:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto !== null\"><label class=label-component>Tipo de Projeto:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome !== null\"><label class=label-component>Nome da Edificação:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes !== null\"><label class=label-component>Observações Gerais:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes }}</span></span></td><td md-cell><div id=unidadeAutonomaEdificacao><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao !== null\"><label class=label-component>Tipo de Edificação:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao.descricao, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador !== null\"><label class=label-component>Possui Elevador:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto !== null\"><label class=label-component>Tipo de Projeto:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto.descricao, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome !== null\"><label class=label-component>Nome da Edificação:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes !== null\"><label class=label-component>Observações Gerais:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes }}</span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Edificacao Unidade Autônoma\" name=unidadeAutonomaEdificacao ng-model=homCtrl.informacoesRecusada.unidadeAutonomaEdificacao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA_EDIFICACAO)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Condições de Acessibilidade</label><br><label class=label-component>Há vagas próximas dos acessos de circulação de pedestres devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade ? \"Sem Informação\" : homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga ? homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga : \"Sem Informação.\"}}</span><br><label class=label-component>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade ? \"Sem Informação\" : homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade ? homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade : \"Sem Informação.\"}}</span><br><label class=label-component>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade ? \"Sem Informação\" : homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro ? homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro : \"Sem Informação.\"}}</span><br><label class=label-component>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade ? \"Sem Informação\" : homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador ? homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador : \"Sem Informação.\"}}</span></td><td md-cell><div id=unidadeAutonomaAcessibilidade><label class=label-titulo>Condições de Acessibilidade</label><br><label class=label-component>Há vagas próximas dos acessos de circulação de pedestres devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade? \"Sem Informação\" : homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga ? homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga : \"Sem Informação.\"}}</span><br><label class=label-component>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade? \"Sem Informação\" : homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade ? homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade : \"Sem Informação.\"}}</span><br><label class=label-component>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade? \"Sem Informação\" : homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro ? homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro : \"Sem Informação.\"}}</span><br><label class=label-component>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade? \"Sem Informação\" : homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador ? homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador : \"Sem Informação.\"}}</span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Acessibilidade Unidade Autônoma\" name=unidadeAutonomaAcessibilidade ng-model=homCtrl.informacoesRecusada.unidadeAutonomaAcessibilidade ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA_ACESSIBILIDADE)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Dados do Medidor</label><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.unidadeAutonoma.edificacao.dadosMedidor\"><br><span ng-show=\"itemOriginal.empresaFornecedora.tipoFornecedoraEnum !== null\"><label class=label-component>Tipo do Medidor:</label><span class=span-original-component>{{ itemOriginal.empresaFornecedora.tipoFornecedoraEnum.tipoFornecedora }}</span><br></span><span ng-show=\"itemOriginal.codigo !== null\"><label class=label-component>Código do medidor:</label><span class=span-original-component>{{ itemOriginal.codigo }}</span><br></span><span ng-show=\"itemOriginal.empresaFornecedora !== null\"><label class=label-component>Empresa Fornecedora:</label><span class=span-original-component>{{ itemOriginal.empresaFornecedora.nome }}</span><br></span></span></td><td md-cell><div id=unidadeAutonomaEdificacaoDadosMedidor><label class=label-titulo>Dados do Medidor</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.unidadeAutonoma.edificacao.dadosMedidor\"><br><span ng-show=\"itemAlterado.empresaFornecedora.tipoFornecedoraEnum !== null\"><label class=label-component>Tipo do Medidor:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.dadosMedidor[$index].empresaFornecedora.tipoFornecedoraEnum, itemAlterado.empresaFornecedora.tipoFornecedoraEnum) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.empresaFornecedora.tipoFornecedoraEnum.tipoFornecedora }}</span><br></span><span ng-show=\"itemAlterado.codigo !== null\"><label class=label-component>Código do medidor:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.dadosMedidor[$index].codigo, itemAlterado.codigo ) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.codigo }}</span><br></span><span ng-show=\"itemAlterado.empresaFornecedora !== null\"><label class=label-component>Empresa Fornecedora:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.dadosMedidor[$index].empresaFornecedora.nome, itemAlterado.empresaFornecedora.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.empresaFornecedora.nome }}</span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Unidade Autônoma Edificação Dados Medidor \" name=unidadeAutonomaEdificacaoDadosMedidor ng-model=homCtrl.informacoesRecusada.unidadeAutonomaEdificacaoDadosMedidor ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA_EDIFICACAO_DADOS_MEDIDOR)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Documentação</label><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao\"><br><span ng-show=\"itemOriginal.nome !== null\"><label class=label-component>Documento:</label><span class=span-original-component>{{ itemOriginal.nome }}</span><br></span><span ng-show=\"itemOriginal.dataEmissao !== null\"><label class=label-component>Data de Emissão:</label><span class=span-original-component>{{ itemOriginal.dataEmissao | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.dataValidade !== null\"><label class=label-component>Data de Validade:</label><span class=span-original-component>{{ itemOriginal.dataValidade | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.arquivo !== null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.arquivo.url }}\" download=\"{{ itemOriginal.arquivo.nomeOriginal }}\">{{ itemOriginal.arquivo.nomeOriginal }}</a></span><br></span></span></td><td md-cell><div id=unidadeAutonomaEdificacaoDocumentacao><label class=label-titulo>Documentação</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.unidadeAutonoma.edificacao.documentacao\"><br><span ng-show=\"itemAlterado.nome !== null\"><label class=label-component>Documento:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao[$index].nome, itemAlterado.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.nome }}</span><br></span><span ng-show=\"itemAlterado.dataEmissao !== null\"><label class=label-component>Data de Emissão:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao[$index].dataEmissao, itemAlterado.dataEmissao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataEmissao | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.dataValidade !== null\"><label class=label-component>Data de Validade:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao[$index].dataValidade, itemAlterado.dataValidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataValidade | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.arquivo !== null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao[$index].arquivo.nome, itemAlterado.arquivo.nome) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.arquivo.url }}\" download=\"{{ itemAlterado.arquivo.nomeOriginal }}\">{{ itemAlterado.arquivo.nomeOriginal }}</a></span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Unidade Autônoma Edificação Documentação\" name=unidadeAutonomaEdificacaoDocumentacao ng-model=homCtrl.informacoesRecusada.unidadeAutonomaEdificacaoDocumentacao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA_EDIFICACAO_DOCUMENTACAO)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-unidade-autonoma/detalhar-imovel-dados-unidade-autonoma-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Dados da Unidade Autônoma</label><br><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem === null\"><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.area !== null\"><label class=label-component>Área Construída (m²):</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.area }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios !== null\"><label class=label-component>Número de Dormitórios:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial != null || homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral != null\"><label class=label-component>Fração ideal do imóvel:</label><span class=span-original-component><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial != null\">{{ homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial }} </span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral != null\">{{ homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral }}</span></span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo !== null\"><label class=label-component>Memorial Descritivo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem !== null\"><label class=label-component>Possui Garagem:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas !== null\"><label class=label-component>Nº de Vagas Cobertas:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas !== null\"><label class=label-component>Nº de Vagas Descobertas:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.observacoes !== null\"><label class=label-component>Observações Gerais:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.observacoes }}</span><br></span></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem !== null\"><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.area !== null\"><label class=label-component>Área (m²):</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.area }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem !== null\"><label class=label-component>Tipo de Garagem:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo !== null\"><label class=label-component>Memorial Descritivo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo }}</span></span></span></td><td md-cell><div id=unidadeAutonoma><label class=label-titulo>Dados da Unidade Autônoma</label><br><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem === null\"><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.area !== null\"><label class=label-component>Área Construída (m²):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.area }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios !== null\"><label class=label-component>Número de Dormitórios:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios, homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial !== null || homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral\"><label class=label-component>Fração ideal do imóvel:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial) ? \'span-alterado-component\' : \'span-original-component\'\" ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial != null\">{{ homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial }} </span><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral) ? \'span-alterado-component\' : \'span-original-component\'\" ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral != null\">{{ homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo !== null\"><label class=label-component>Memorial Descritivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem !== null\"><label class=label-component>Possui Garagem:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem, homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas !== null\"><label class=label-component>Nº de Vagas Cobertas:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas !== null\"><label class=label-component>Nº de Vagas Descobertas:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.observacoes !== null\"><label class=label-component>Observações Gerais:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.observacoes) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.observacoes }}</span><br></span></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem !== null\"><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.area !== null\"><label class=label-component>Área (m²):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.area }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem !== null\"><label class=label-component>Tipo de Garagem:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem.descricao, homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo !== null\"><label class=label-component>Memorial Descritivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo }}</span></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Unidade Autonoma\" name=unidadeAutonoma ng-model=homCtrl.informacoesRecusada.unidadeAutonoma ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.plantas.length != 0 \"><td md-cell ng-show=\"homCtrl.isCadastroAlterado() && homCtrl.imovelOriginal.unidadeAutonoma.plantas.length != 0\"><label class=label-titulo>Plantas da Unidade Autônoma</label><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.unidadeAutonoma.plantas\"><br><span ng-show=\"itemOriginal.descricao !== null\"><label class=label-component>Descrição:</label><span class=span-original-component>{{ itemOriginal.descricao }}</span><br></span><span ng-show=\"itemOriginal.data !== null\"><label class=label-component>Data:</label><span class=span-original-component>{{ itemOriginal.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.arquivo !== null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.arquivo.url }}\" download=\"{{ itemOriginal.arquivo.nomeOriginal }}\">{{ itemOriginal.arquivo.nomeOriginal}}</a></span><br></span></span></td><td md-cell><div id=unidadeAutonomaPlantas ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.plantas.length != 0\"><label class=label-titulo>Plantas da Unidade Autônoma</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.unidadeAutonoma.plantas\"><br><span ng-show=\"itemAlterado.descricao !== null\"><label class=label-component>Descrição:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.plantas[$index].descricao, itemAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.descricao }}</span><br></span><span ng-show=\"itemAlterado.data !== null\"><label class=label-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.plantas[$index].data, itemAlterado.data) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.arquivo !== null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.plantas[$index].nomeArquivo, itemAlterado.nomeArquivo) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.arquivo.url }}\" download=\"{{ itemAlterado.arquivo.nomeOriginal }}\">{{ itemAlterado.arquivo.nomeOriginal }}</a></span><br></span></span></div></td><td md-cell class=coluna-recusar ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.plantas.length != 0\"><md-checkbox aria-label=\"Recusar Plantas Unidade Autônoma\" name=unidadeAutonomaPlantas ng-model=homCtrl.informacoesRecusada.unidadeAutonomaPlantas ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA_PLANTAS)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><div style=\"margin-top: 5px\"><label class=label-titulo>Fotos da Unidade Autônoma</label><br><span><inc-modal-fotos titulo-modal=\"Fotos da Unidade Autônoma\" fotos=homCtrl.imovelOriginal.unidadeAutonoma.fotos></inc-modal-fotos></span></div></td><td md-cell><div style=\"margin-top: 5px\"><div id=fotosUnidadeAutonoma><label class=label-titulo style=\"margin-top: 5px\">Fotos da Unidade Autônoma</label><br><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fotos , homCtrl.imovelAlterado.unidadeAutonoma.fotos) ? \'span-alterado-component\' : \'span-original-component\'\"><inc-modal-fotos titulo-modal=\"Fotos da Unidade Autônoma\" fotos=homCtrl.imovelAlterado.unidadeAutonoma.fotos></inc-modal-fotos></span></div></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Fotos do Imóvel\" name=fotosUnidadeAutonoma ng-model=homCtrl.informacoesRecusada.fotosUnidadeAutonoma ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.FOTOS_UNIDADE_AUTONOMA)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-unidade-autonoma/detalhar-imovel-dados-unidade-autonoma.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados da Unidade Autônoma</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Área Construída (m²)</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.area }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.area }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área construída\" name=areaConstruidaUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area)\" ng-checked=\"homCtrl.exists(\'areaConstruidaUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número de Dormitórios</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios, homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número de dormitórios\" name=numeroDormitoriosUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios, homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios)\" ng-checked=\"homCtrl.exists(\'numeroDormitoriosUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Memorial Descritivo</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo\" name=memorialDescritivoUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Possui Garagem</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.boolToString(homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem) }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem, homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem) ? \'dado-alterado\' : \'\'\">{{ homCtrl.boolToString(homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem) }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar posse garagem\" name=possuiGaragemUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem, homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem)\" ng-checked=\"homCtrl.exists(\'possuiGaragemUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número de Vagas Cobertas</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número de vagas cobertas\" name=numeroVagasCobertasUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas)\" ng-checked=\"homCtrl.exists(\'numeroVagasCobertasUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número de Vagas Descobertas</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número de vagas descobertas\" name=numeroVagasDescobertasUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas)\" ng-checked=\"homCtrl.exists(\'numeroVagasDescobertasUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Observações Gerais</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.observacoes }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.observacoes) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.observacoes }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar observações\" name=observacoesUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.observacoes)\" ng-checked=\"homCtrl.exists(\'observacoesUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Fotos da Unidade Autônoma</th></tr><tr md-row><th md-column width=45%>Original</th><th md-column width=45%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell><span ng-show=homCtrl.isCadastroAlterado()><inc-modal-fotos titulo-modal=\"Fotos da Unidade Autônoma\" fotos=homCtrl.imovelOriginal.unidadeAutonoma.fotos></inc-modal-fotos></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fotos, homCtrl.imovelAlterado.unidadeAutonoma.fotos) ? \'dado-alterado\' : \'\'\"><inc-modal-fotos titulo-modal=\"Fotos da Unidade Autônoma\" fotos=homCtrl.imovelAlterado.unidadeAutonoma.fotos></inc-modal-fotos></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fotos da unidade autônoma\" name=fotosUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.fotos, homCtrl.imovelAlterado.unidadeAutonoma.fotos)\" ng-checked=\"homCtrl.exists(\'fotosUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Plantas da Unidade Autônoma</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Plantas</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"planta in homCtrl.imovelOriginal.unidadeAutonoma.plantas\"><a ng-href=\"{{ planta.arquivo.url }}\" download=\"{{ planta.arquivo.nomeOriginal }}\">{{ planta.arquivo.nomeOriginal }}</a></span></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.plantas, homCtrl.imovelAlterado.unidadeAutonoma.plantas) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"planta in homCtrl.imovelAlterado.unidadeAutonoma.plantas\"><a ng-href=\"{{ planta.arquivo.url }}\" download=\"{{ planta.arquivo.nomeOriginal }}\">{{ planta.arquivo.nomeOriginal }}</a></span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar plantas da unidade autônoma\" name=plantasUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.plantas, homCtrl.imovelAlterado.unidadeAutonoma.plantas)\" ng-checked=\"homCtrl.exists(\'plantasUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados da Garagem</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Área (m²)</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.area }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.area }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área da garagem\" name=areaGaragem ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area)\" ng-checked=\"homCtrl.exists(\'areaGaragem\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Tipo de Garagem</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem.id, homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de garagem\" name=tipoGaragem ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem.id, homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem.id)\" ng-checked=\"homCtrl.exists(\'tipoGaragem\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Memorial Descritivo</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo da garagem\" name=memorialDescritivoGaragem ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoGaragem\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-terreno/detalhar-imovel-dados-terreno-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Dados Cartoriais</label><br><span ng-show=\"homCtrl.imovelOriginal.terreno.tipoGeometriaCartorial !== null\"><label class=label-component>Geometria não-geoferenciada:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.tipoGeometriaCartorial === \"NAO_GEORREFERENCIADA\" ? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-component>Arquivo (zip,csv e txt):</label><span class=span-original-component><a ng-href=\"{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.url }}\" download=\"{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.nome }}\">{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.nome }}</a></span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Data:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.dataPlanta | date: \"dd/MM/yyyy\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Escala de Vetorização:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.escala}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Forma de Levantamento:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.tipoFormaLevantamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Resolução Espacial (m):</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.resolucaoEspacial}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Tipo de Equipamento Utilizado:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.tipoEquipamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial !== null\"><label class=label-component>Memorial Descritivo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial !== null\"><label class=label-component>Unidade de Medida da Área:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial.unidade }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-component>Área da Geometria do Terreno:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.areaGeometria}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.areaTotalCartorial !== null\"><label class=label-component>Área Total:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.areaTotalCartorial|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.areaUniaoCartorial !== null\"><label class=label-component>Área da União:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.areaUniaoCartorial|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao !== null\"><label class=label-component>Memorial Descritivo da Área da União:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao }}</span></span></td><td md-cell><div id=dadosCartoriais><label class=label-titulo>Dados Cartoriais</label><br><span ng-show=\"homCtrl.imoveAlterado.terreno.tipoGeometriaCartorial !== null\"><label class=label-component>Geometria não-geoferenciada:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imoveOriginal.terreno.tipoGeometriaCartorial, homCtrl.imoveAlterado.terreno.tipoGeometriaCartorial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imoveAlterado.terreno.tipoGeometriaCartorial === \"NAO_GEORREFERENCIADA\" ? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-component>Arquivo (zip,csv e txt):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.nome, homCtrl.imovelAlterado.terreno.arquivoCartorial.nome) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.url }}\" download=\"{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.nome }}\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.nome }}</a></span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.dataPlanta, homCtrl.imovelAlterado.terreno.arquivoCartorial.dataPlanta) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.dataPlanta | date: \"dd/MM/yyyy\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Escala de Vetorização:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.escala, homCtrl.imovelAlterado.terreno.arquivoCartorial.escala) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.escala}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Forma de Levantamento:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.tipoFormaLevantamento.descricao, homCtrl.imovelAlterado.terreno.arquivoCartorial.tipoFormaLevantamento.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.tipoFormaLevantamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Resolução Espacial (m):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.resolucaoEspacial, homCtrl.imovelAlterado.terreno.arquivoCartorial.resolucaoEspacial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.resolucaoEspacial}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Tipo de Equipamento Utilizado:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.tipoEquipamento.descricao, homCtrl.imovelAlterado.terreno.arquivoCartorial.tipoEquipamento.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.tipoEquipamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial !== null\"><label class=label-component>Memorial Descritivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial, homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial !== null\"><label class=label-component>Unidade de Medida da Área:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial.unidade, homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.unidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.unidade }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-component>Área da Geometria do Terreno:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.areaGeometria, homCtrl.imovelAlterado.terreno.arquivoCartorial.areaGeometria) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.areaGeometria}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.areaTotalCartorial !== null\"><label class=label-component>Área Total:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaTotalCartorial, homCtrl.imovelAlterado.terreno.areaTotalCartorial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.areaTotalCartorial|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.areaUniaoCartorial !== null\"><label class=label-component>Área da União:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaUniaoCartorial, homCtrl.imovelAlterado.terreno.areaUniaoCartorial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.areaUniaoCartorial|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao !== null\"><label class=label-component>Memorial Descritivo da Área da União:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao,  homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao }}</span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Dados Cartoriais\" name=dadosCartoriais ng-model=homCtrl.informacoesRecusada.dadosCartoriais ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.DADOS_CARTORIAIS)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Dados Cadastrais</label><br><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-component>Arquivo (zip,csv e txt):</label><span class=span-original-component><a ng-href=\"{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.url }}\" download=\"{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.nome }}\">{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.nome }}</a></span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Data:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.dataPlanta | date: \"dd/MM/yyyy\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Escala de Vetorização:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.escala}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Forma de Levantamento:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.tipoFormaLevantamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Resolução Espacial (m):</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.resolucaoEspacial}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Tipo de Equipamento Utilizado:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.tipoEquipamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral !== null\"><label class=label-component>Memorial Descritivo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.unidadeMedidaCadastral !== null\"><label class=label-component>Unidade de Medida da Área:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.unidadeMedidaCadastral }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-component>Área da Geometria do Terreno:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.areaGeometria}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.areaTotalCadastral !== null\"><label class=label-component>Área Total:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.areaTotalCadastral|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.areaUniaoCadastral !== null\"><label class=label-component>Área da União:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.areaUniaoCadastral|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao !== null\"><label class=label-component>Memorial Descritivo da Área da União:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao }}</span></span></td><td md-cell><div id=dadosCadastrais><label class=label-titulo>Dados Cadastrais</label><br><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-component>Arquivo (zip,csv e txt):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.nome, homCtrl.imovelAlterado.terreno.arquivoCadastral.nome) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.url }}\" download=\"{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.nome }}\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.nome }}</a></span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.dataPlanta, homCtrl.imovelAlterado.terreno.arquivoCadastral.dataPlanta) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.dataPlanta | date: \"dd/MM/yyyy\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Escala de Vetorização:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.escala, homCtrl.imovelAlterado.terreno.arquivoCadastral.escala) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.escala }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Forma de Levantamento:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.tipoFormaLevantamento.descricao, homCtrl.imovelAlterado.terreno.arquivoCadastral.tipoFormaLevantamento.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.tipoFormaLevantamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Resolução Espacial (m):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.resolucaoEspacial, homCtrl.imovelAlterado.terreno.arquivoCadastral.resolucaoEspacial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.resolucaoEspacial}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Tipo de Equipamento Utilizado:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.tipoEquipamento.descricao, homCtrl.imovelAlterado.terreno.arquivoCadastral.tipoEquipamento.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.tipoEquipamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral !== null\"><label class=label-component>Memorial Descritivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral, homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral !== null\"><label class=label-component>Unidade de Medida da Área:</label><span ng-class=\"homCtrl.isDadoDivergente() ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-component>Área da Geometria do Terreno:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.areaGeometria, homCtrl.imovelAlterado.terreno.arquivoCadastral.areaGeometria) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.areaGeometria}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.areaTotalCadastral !== null\"><label class=label-component>Área Total:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaTotalCadastral, homCtrl.imovelAlterado.terreno.areaTotalCadastral) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.areaTotalCadastral|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.areaUniaoCadastral !== null\"><label class=label-component>Área da União:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaUniaoCadastral, homCtrl.imovelAlterado.terreno.areaUniaoCadastral) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.areaUniaoCadastral|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao !== null\"><label class=label-component>Memorial Descritivo da Área da União:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao,  homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao }}</span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Dados Cadastrais\" name=dadosCadastrais ng-model=homCtrl.informacoesRecusada.dadosCadastrais ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.DADOS_CADASTRAIS)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Restriçoes de Uso/Ocupação</label><br><span ng-show=\"homCtrl.imovelOriginal.terreno.isAreaInteresseSocial !== null\"><label class=label-component>Zona/Área de Interesse Social (ZEIS):</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.isAreaInteresseSocial ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.isAreaSeguranca !== null\"><label class=label-component>Situado em área de segurança:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.isAreaSeguranca ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.isAreaNonAedificanti !== null\"><label class=label-component>Área non Aedificandi:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.isAreaNonAedificandi ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.isPatrimonioTombado !== null\"><label class=label-component>Tombado como patrimônio histórico, artístico e/ou cultural:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.isPatrimonioTombado ? homCtrl.imovelOriginal.terreno.descricaoPatrimonioTombado : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.isOutraRestricao !== null\"><label class=label-component>Outra Restrição de uso/ocupação:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.isOutraRestricao ? homCtrl.imovelOriginal.terreno.descricaoOutraRestricao : \"Não\"}}</span></span></td><td md-cell><div id=restricoesUsoOcupacao><label class=label-titulo>Restriçoes de Uso/Ocupação</label><br><span ng-show=\"homCtrl.imovelAlterado.terreno.isAreaInteresseSocial !== null\"><label class=label-component>Zona/Área de Interesse Social (ZEIS):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaInteresseSocial, homCtrl.imovelAlterado.terreno.isAreaInteresseSocial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.isAreaInteresseSocial ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.isAreaSeguranca !== null\"><label class=label-component>Situado em área de segurança:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaSeguranca, homCtrl.imovelAlterado.terreno.isAreaSeguranca) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.isAreaSeguranca ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.isAreaNonAedificanti !== null\"><label class=label-component>Área non Aedificandi:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaNonAedificanti, homCtrl.imovelAlterado.terreno.isAreaNonAedificanti) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.isAreaNonAedificandi ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.isPatrimonioTombado !== null\"><label class=label-component>Tombado como patrimônio histórico, artístico e/ou cultural:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isPatrimonioTombado, homCtrl.imovelAlterado.terreno.isPatrimonioTombado) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.isPatrimonioTombado ? homCtrl.imovelAlterado.terreno.descricaoPatrimonioTombado : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.isOutraRestricao !== null\"><label class=label-component>Outra Restrição de uso/ocupação:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isOutraRestricao, homCtrl.imovelAlterado.terreno.isOutraRestricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.isOutraRestricao ? homCtrl.imovelAlterado.terreno.descricaoOutraRestricao : \"Não\"}}</span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=Recusar name=restricoesUsoOcupacao ng-model=homCtrl.informacoesRecusada.restricoesUsoOcupacao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.RESTRICOES_USO_OCUPACAO)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.arquivosComplementaresLista.length !== 0 || homCtrl.imovelAlterado.arquivosComplementaresLista.length !== 0\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Arquivos Complementares</label><br><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.arquivosComplementaresLista\"><label class=label-component>Descrição:</label><span class=span-original-component>{{ itemOriginal.descricao }}</span><br><label class=label-component>Data:</label><span class=span-original-component>{{ itemOriginal.data | date: \"dd/MM/yyyy\" }}</span><br><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.arquivo.url }}\" download=\"{{ itemOriginal.arquivo.nomeOriginal }}\">{{ itemOriginal.arquivo.nomeOriginal }}</a></span><br><br></span></td><td md-cell><div id=arquivosComplementaresTerreno><label class=label-titulo>Arquivos Complementares</label><br><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.arquivosComplementaresLista\"><label class=label-component>Descrição:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.arquivosComplementaresLista[$index].descricao, itemAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.descricao }}</span><br><label class=label-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.arquivosComplementaresLista[$index].data, itemAlterado.data) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.data | date: \"dd/MM/yyyy\" }}</span><br><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.arquivosComplementaresLista[$index].arquivo.nomeOriginal, itemAlterado.arquivo.nomeOriginal) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.arquivo.url }}\" download=\"{{ itemAlterado.arquivo.nomeOriginal }}\">{{ itemAlterado.arquivo.nomeOriginal }}</a></span><br><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=Recusar name=arquivosComplementaresTerreno ng-model=homCtrl.informacoesRecusada.arquivosComplementaresTerreno ng-click=homCtrl.toggle(vm.CONSTANTES_IMOVEL_RECUSADO.ARQUIVOS_COMPLEMENTARES_TERRENO)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-terreno/detalhar-imovel-dados-terreno.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Geometria Cartorial do Terreno</th></tr><tr md-row><th md-column width=45%; ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=45%;>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.geometriaTerrenoCartorial != null\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><inc-mapa-homologar geometria=homCtrl.imovelOriginal.terreno.geometriaTerrenoCartorial></inc-mapa-homologar></td><td md-cell><inc-mapa-homologar geometria=homCtrl.imovelAlterado.terreno.geometriaTerrenoCartorial dado-alterado=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.geometriaTerrenoCartorial.coordinates, homCtrl.imovelAlterado.terreno.geometriaTerrenoCartorial.coordinates)\"></inc-mapa-homologar></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar geometria cartorial\" name=geometriaCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.geometriaTerrenoCartorial.coordinates, homCtrl.imovelAlterado.terreno.geometriaTerrenoCartorial.coordinates)\" ng-checked=\"homCtrl.exists(\'geometriaCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados de Cartoriais</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"typeof(homCtrl.imovelAlterado.terreno.isCertificaoIncra) != \'undefined\'\"><td md-cell>Levantamento Cadastral Certificado pelo Incra</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isCertificaoIncra ? \"Sim\" : \"Não\" }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isCertificaoIncra, homCtrl.imovelAlterado.terreno.isCertificaoIncra) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isCertificaoIncra ? \"Sim\" : \"Não\" }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar certificação Incra\" name=certificadoPeloIncra ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isCertificaoIncra, homCtrl.imovelAlterado.terreno.isCertificaoIncra)\" ng-checked=\"homCtrl.exists(\'certificadoPeloIncra\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial != null\"><td md-cell>Memorial Descritivo</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial, homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo cartorial\" name=memorialDescritivoCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial, homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.unidade != null\"><td md-cell>Unidade de Medida da Área</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial.unidade }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial.name, homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.name) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.unidade }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar unidade de medida da área cartorial\" name=unidadeMedidaCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial.name, homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.name)\" ng-checked=\"homCtrl.exists(\'unidadeMedidaCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.areaTotalCartorial != null\"><td md-cell>Área Total(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.areaTotalCartorial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaTotalCartorial, homCtrl.imovelAlterado.terreno.areaTotalCartorial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.areaTotalCartorial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área total cartorial\" name=areaTotalCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.areaTotalCartorial, homCtrl.imovelAlterado.terreno.areaTotalCartorial)\" ng-checked=\"homCtrl.exists(\'areaTotalCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.areaUniaoCartorial != null\"><td md-cell>Área da União(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.areaUniaoCartorial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaUniaoCartorial, homCtrl.imovelAlterado.terreno.areaUniaoCartorial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.areaUniaoCartorial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área da união cartorial\" name=areaUniaoCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.areaUniaoCartorial, homCtrl.imovelAlterado.terreno.areaUniaoCartorial)\" ng-checked=\"homCtrl.exists(\'areaUniaoCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial != null\"><td md-cell>Fração Ideal do Imóvel(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fração ideal do imóvel cartorial\" name=fracaoIdealCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial)\" ng-checked=\"homCtrl.exists(\'fracaoIdealCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCartorial != null\"><td md-cell>Fração Ideal da União(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCartorial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCartorial, homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCartorial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCartorial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fração ideal da união cartorial\" name=fracaoIdealUniaoCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCartorial, homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCartorial)\" ng-checked=\"homCtrl.exists(\'fracaoIdealUniaoCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao != null\"><td md-cell>Memorial Descritivo da Área da União</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao, homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo da união cartorial\" name=memorialDescritivoCartorialUniao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao, homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoCartorialUniao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Geometria Cadastral do Terreno</th></tr><tr md-row><th md-column width=45%; ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=45%;>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.geometriaTerreno != null\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><inc-mapa-homologar geometria=homCtrl.imovelOriginal.terreno.geometriaTerreno></inc-mapa-homologar></td><td md-cell><inc-mapa-homologar geometria=homCtrl.imovelAlterado.terreno.geometriaTerreno dado-alterado=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.geometriaTerreno.coordinates, homCtrl.imovelAlterado.terreno.geometriaTerreno.coordinates)\"></inc-mapa-homologar></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar geometria cadastral\" name=geometriaCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.geometriaTerreno.coordinates, homCtrl.imovelAlterado.terreno.geometriaTerreno.coordinates)\" ng-checked=\"homCtrl.exists(\'geometriaCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados de Cadastrais</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral != null\"><td md-cell>Memorial Descritivo</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral, homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo cadastral\" name=memorialDescritivoCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral, homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral != null\"><td md-cell>Unidade de Medida da Área</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.unidadeMedidaCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.unidadeMedidaCadastral.name, homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral.name) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar unidade de medida da área cadastral\" name=unidadeMedidaCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.unidadeMedidaCadastral.name, homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral.name)\" ng-checked=\"homCtrl.exists(\'unidadeMedidaCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.areaTotalCadastral != null\"><td md-cell>Área Total(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.areaTotalCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaTotalCadastral, homCtrl.imovelAlterado.terreno.areaTotalCadastral) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.areaTotalCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área total cadastral\" name=areaTotalCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.areaTotalCadastral, homCtrl.imovelAlterado.terreno.areaTotalCadastral)\" ng-checked=\"homCtrl.exists(\'areaTotalCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.areaUniaoCadastral != null\"><td md-cell>Área da União(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.areaUniaoCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaUniaoCadastral, homCtrl.imovelAlterado.terreno.areaUniaoCadastral) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.areaUniaoCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área da união cadastral\" name=areaUniaoCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.areaUniaoCadastral, homCtrl.imovelAlterado.terreno.areaUniaoCadastral)\" ng-checked=\"homCtrl.exists(\'areaUniaoCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral != null\"><td md-cell>Fração Ideal do Imóvel(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fração ideal do imóvel cadastral\" name=fracaoIdealCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral)\" ng-checked=\"homCtrl.exists(\'fracaoIdealCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCadastral != null\"><td md-cell>Fração Ideal da União(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCadastral, homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCadastral) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fração ideal da união cadastral\" name=fracaoIdealUniaoCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCadastral, homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCadastral)\" ng-checked=\"homCtrl.exists(\'fracaoIdealUniaoCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao != null\"><td md-cell>Memorial Descritivo da Área da União</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao, homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo da união cadastral\" name=memorialDescritivoCadastralUniao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao, homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoCadastralUniao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Restrições de Uso/Ocupação</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.isAreaInteresseSocial != null\"><td md-cell>Zona/Área de Interesse Social (ZEIS)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isAreaInteresseSocial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaInteresseSocial, homCtrl.imovelAlterado.terreno.isAreaInteresseSocial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isAreaInteresseSocial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área de interesse social\" name=areaInteresseSocial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isAreaInteresseSocial, homCtrl.imovelAlterado.terreno.isAreaInteresseSocial)\" ng-checked=\"homCtrl.exists(\'areaInteresseSocial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.isAreaSeguranca != null\"><td md-cell>Situado em Área de Segurança</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isAreaSeguranca }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaSeguranca, homCtrl.imovelAlterado.terreno.isAreaSeguranca) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isAreaSeguranca }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área de segurança\" name=areaSeguranca ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isAreaSeguranca, homCtrl.imovelAlterado.terreno.isAreaSeguranca)\" ng-checked=\"homCtrl.exists(\'areaSeguranca\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.isPatrimonioTombado != null\"><td md-cell>Tombado Como Patrimônio Histórico, Artístico e/ou Cultural</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isPatrimonioTombado }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isPatrimonioTombado, homCtrl.imovelAlterado.terreno.isPatrimonioTombado) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isPatrimonioTombado }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tombado como patrimônio\" name=patrimonioTombado ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isPatrimonioTombado, homCtrl.imovelAlterado.terreno.isPatrimonioTombado)\" ng-checked=\"homCtrl.exists(\'patrimonioTombado\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.isAreaNonAedificandi != null\"><td md-cell>Área Non Aedificandi</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isAreaNonAedificandi }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaNonAedificandi, homCtrl.imovelAlterado.terreno.isAreaNonAedificandi) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isAreaNonAedificandi }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área non aedificandi\" name=areaNonAedificandi ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isAreaNonAedificandi, homCtrl.imovelAlterado.terreno.isAreaNonAedificandi)\" ng-checked=\"homCtrl.exists(\'areaNonAedificandi\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.isOutraRestricao != null\"><td md-cell>Outra Restrição de Uso/Ocupação</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isOutraRestricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isOutraRestricao, homCtrl.imovelAlterado.terreno.isOutraRestricao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isOutraRestricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar outra restrição\" name=outraRestricao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isOutraRestricao, homCtrl.imovelAlterado.terreno.isOutraRestricao)\" ng-checked=\"homCtrl.exists(\'outraRestricao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Arquivos Complementares</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelOriginal.arquivosComplementaresLista.length > 0 || homCtrl.imovelAlterado.arquivosComplementaresLista.length > 0\"><td md-cell>Arquivos Complementares</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"arquivoComplementar in homCtrl.imovelOriginal.arquivosComplementaresLista\"><a ng-href=\"{{ arquivoComplementar.arquivo.url }}\" download=\"{{ arquivoComplementar.arquivo.nomeOriginal }}\">{{ arquivoComplementar.arquivo.nomeOriginal }}</a></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivosComplementares, homCtrl.imovelAlterado.terreno.arquivosComplementares) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"arquivoComplementar in homCtrl.imovelAlterado.arquivosComplementaresLista\"><a ng-href=\"{{ arquivoComplementar.arquivo.url }}\" download=\"{{ arquivoComplementar.arquivo.nomeOriginal }}\">{{ arquivoComplementar.arquivo.nomeOriginal }}</a></span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar arquivos complementares\" name=arquivosComplementares ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.arquivosComplementares, homCtrl.imovelAlterado.terreno.arquivosComplementares)\" ng-checked=\"homCtrl.exists(\'arquivosComplementares\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/acessao-benfeitoria.tmpl.html","<div layout=column><md-input-container flex><label>Terreno com Acessão/Benfeitoria?</label><md-select ng-model=imovel.terreno.benfeitoria class=secaoBenfeitoria aria-label=\"Terreno com Acessão/Benfeitoria?\" placeholder=\"Selecione uma opção\" ng-required=imovelCtrl.showAcenssaoBenfeitoria ng-disabled=\"imovel.terreno.benfeitoria === \'Sim\' || vm.isDetail\"><md-option value=\"Não informado\">Não informado</md-option><md-option value=Sim>Sim</md-option><md-option value=Não>Não</md-option></md-select></md-input-container><span class=form-error ng-show=\"forms.terrenoForm.terrenoAcessaoBenfeitoria.$error.required && submittedTerreno\">Campo de preenchimento obrigatório</span></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/arquivos-complementares.html","<div layout=row flex=100><div layout=column flex=100><div><upload-arquivos is-detail=vm.isDetail model=imovel.arquivosComplementaresLista></upload-arquivos></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/condicoes-acessibilidade.tmpl.html","<div layout=column><div layout=row><md-input-container><md-checkbox class=secaoCondicoesAcessibilidade ng-model=imovel.unidadeAutonoma.semInfoAcessibilidade ng-click=imovelCtrl.desselecionaRadioButtons(); ng-disabled=vm.isDetail>Sem Informações de acessibilidade</md-checkbox></md-input-container></div><div layout=row ng-hide=isGaragem><md-input-container>Há vagas próximas dos acessos de circulação de pedestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.<span ng-show=!imovel.unidadeAutonoma.semInfoAcessibilidade>*</span></md-input-container></div><div layout=row ng-hide=isGaragem><md-radio-group name=vagasProximasAcessoCirculacao class=secaoCondicoesAcessibilidade ng-model=imovel.unidadeAutonoma.vagasProximasAcessoCirculacao ng-required=\"!imovel.unidadeAutonoma.semInfoAcessibilidade && !isGaragem\" ng-disabled=vm.isDetail><md-radio-button id=radioVagasProximasDeficientesSim ng-value=true style=\"float: left; margin-left: 20px !important\" ng-disabled=\"imovel.unidadeAutonoma.semInfoAcessibilidade || vm.isDetail\">Sim</md-radio-button><md-radio-button id=radioVagasProximasDeficientesNao ng-value=false style=\"float: left; margin-left: 20px; margin-top: 0 !important\" ng-disabled=\"imovel.unidadeAutonoma.semInfoAcessibilidade || vm.isDetail\">Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.vagasProximasAcessoCirculacao.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row><md-input-container>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.<span ng-show=!imovel.unidadeAutonoma.semInfoAcessibilidade>*</span></md-input-container></div><div layout=row><md-radio-group name=acessosLivresBarreirasArquitetonicas class=secaoCondicoesAcessibilidade ng-model=imovel.unidadeAutonoma.acessosLivresBarreirasArquitetonicas ng-required=!imovel.unidadeAutonoma.semInfoAcessibilidade ng-disabled=vm.isDetail><md-radio-button id=radioAcessosLivresBarreirasArquitetonicasSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=\"imovel.unidadeAutonoma.semInfoAcessibilidade || vm.isDetail\">Sim</md-radio-button><md-radio-button id=radioAcessosLivresBarreirasArquitetonicasNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0  !important\" ng-disabled=\"imovel.unidadeAutonoma.semInfoAcessibilidade || vm.isDetail\">Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.acessosLivresBarreirasArquitetonicas.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row ng-hide=isGaragem><md-input-container>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.<span ng-show=!imovel.unidadeAutonoma.semInfoAcessibilidade>*</span></md-input-container></div><div layout=row ng-hide=isGaragem><md-radio-group name=banheiroAcessivel class=secaoCondicoesAcessibilidade ng-model=imovel.unidadeAutonoma.banheiroAcessivel ng-required=\"!imovel.unidadeAutonoma.semInfoAcessibilidade && !isGaragem\"><md-radio-button id=radioBanheiroAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=\"imovel.unidadeAutonoma.semInfoAcessibilidade || vm.isDetail\">Sim</md-radio-button><md-radio-button id=radioBanheiroAcessivelNao ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=\"imovel.unidadeAutonoma.semInfoAcessibilidade || vm.isDetail\">Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.banheiroAcessivel.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div><div layout=row ng-show=\"imovel.unidadeAutonoma.possuiElevador && !isGaragem\"><md-input-container>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.<span ng-show=!imovel.unidadeAutonoma.semInfoAcessibilidade>*</span></md-input-container></div><div layout=row ng-show=\"imovel.unidadeAutonoma.possuiElevador && !isGaragem\"><md-radio-group name=possuiElevador class=secaoCondicoesAcessibilidade ng-model=imovel.unidadeAutonoma.elevadorAcessivel ng-show=imovel.unidadeAutonoma.possuiElevador ng-required=\"imovel.unidadeAutonoma.possuiElevador && !imovel.unidadeAutonoma.semInfoAcessibilidade && !isGaragem\"><md-radio-button id=radioElevadorAcessivelSim ng-value=true style=\"float: left; margin-left:20px !important\" ng-disabled=\"imovel.unidadeAutonoma.semInfoAcessibilidade || vm.isDetail\">Sim</md-radio-button><md-radio-button id=radioElevadorAcessivelNao ng-show=imovel.unidadeAutonoma.possuiElevador ng-value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\" ng-disabled=\"imovel.unidadeAutonoma.semInfoAcessibilidade || vm.isDetail\">Não</md-radio-button></md-radio-group></div><div layout=row><span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.possuiElevador.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/dados-edificacao.tmpl.html","<div layout=column><div layout-gt-xs=row style=\"margin-bottom: 10px\"><md-radio-group class=secaoDadosEdificacao ng-model=vm.unidadeAutonoma.tipoArquivo ng-click=vm.alterarParaArquivoUAShapeFile() style=\"float: left; margin-top: 10px\" ng-disabled=vm.isDetail><md-radio-button id=radio1 ng-value=1212 style=\"float: left\" ng-disabled=vm.isDetail>Arquivo (Shapefile)</md-radio-button><md-radio-button id=radio2 ng-value=12 style=\"float: left; margin-top: 0; margin-left: 15px !important\" ng-disabled=vm.isDetail>Vetorização em Tela</md-radio-button></md-radio-group></div><section layout=row layout-sm=column layout-align=\"right center\" layout-wrap><md-button class=\"md-raised md-primary\" ng-click=vm.carregaArquivoShapeUA() ng-disabled=\"vm.unidadeAutonoma.tipoArquivo != 1212\" ng-hide=vm.isDetail>Escolher Arquivo</md-button>{{ terrenoCtrl.arquivo.nome }}<ng-md-icon ng-hide=\"!terrenoCtrl.arquivo.nome || vm.isDetail\" icon=delete style=\"fill: #788489\" size=18 alt=Excluir ng-click=terrenoCtrl.excluirArquivoTerrenoCadastral($event)></ng-md-icon></section><div layout=row layout-xs=column><md-input-container flex><label>Tipo de edificação</label><md-select class=secaoDadosEdificacao ng-model=imovel.unidadeAutonoma.tipoEdificacao ng-model-options=\"{trackBy : $value.id}\" required ng-class=\"imovel.unidadeAutonoma.tipoEdificacao.id !== imovelOriginal.unidadeAutonoma.tipoEdificacao.id ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-repeat=\"item in vm.tiposEdificacao| orderByBr:\'descricao\'\" ng-value=item ng-selected=\"item.id === imovel.unidadeAutonoma.tipoEdificacao.id\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex ng-show=!vm.isGaragem><label>Tipo de Estrutura</label><md-select class=secaoDadosEdificacao ng-model=imovel.unidadeAutonoma.tipoEstrutura ng-model-options=\"{trackBy : $value.id}\" ng-class=\"imovel.unidadeAutonoma.tipoEstrutura.id !== imovelOriginal.unidadeAutonoma.tipoEstrutura.id ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-repeat=\"item in vm.tiposEstrutura\" ng-value=item ng-selected=\"item.id === imovel.unidadeAutonoma.tipoEstrutura.id\">{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column ng-hide=\"imovel.unidadeAutonoma.tipoEdificacao.id === 4\"><md-input-container><md-checkbox class=secaoDadosEdificacao ng-model=imovel.unidadeAutonoma.possuiElevador ng-class=\"imovel.unidadeAutonoma.possuiElevador !== imovelOriginal.unidadeAutonoma.possuiElevador ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail>Possui elevador</md-checkbox></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/dados-garagem.tmpl.html","<div layout=column><div layout=row><md-input-container flex><label>Área</label><input ng-model=imovel.unidadeAutonoma.areaGaragem ui-number-mask required ng-class=\"imovel.unidadeAutonoma.areaGaragem !== imovelOriginal.unidadeAutonoma.areaGaragem ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></md-input-container><md-input-container flex><label>Tipo de garagem</label><md-select ng-model=imovel.unidadeAutonoma.tipoGaragem ng-model-options=\"{trackBy : $value.id}\" ng-required ng-class=\"imovel.unidadeAutonoma.tipoGaragem !== imovelOriginal.unidadeAutonoma.tipoGaragem ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-value=1>Coberta</md-option><md-option ng-value=2>Descoberta</md-option></md-select></md-input-container></div><div layout=row><md-input-container class=md-block flex><label>Memorial Descritivo</label><textarea ng-model=imovel.unidadeAutonoma.memorialDescritivoGaragem md-maxlength=2000 ng-class=\"imovel.unidadeAutonoma.memorialDescritivoGaragem !== imovelOriginal.unidadeAutonoma.memorialDescritivoGaragem ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></textarea></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/dados-incorporacao.tmpl.html","<div layout=column><div layout=row layout-xs=column><!-- Forma de Incorporação --><md-input-container class=md-block flex><label>Forma de Incorporação</label><md-select class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.tipoIncorporacao ng-model-options=\"{trackBy: \'$value.id\'}\" required name=tipoIncorporacao ng-class=\"vm.imovel.incorporacao.tipoIncorporacao.id !== vm.imovelOriginal.incorporacao.tipoIncorporacao.id ? \'dado-alterado\' : null\" ng-disabled=\"vm.imovel.tipoImovel.id === 3 || vm.imovel.tipoImovel.id === 5 || vm.isDetail\"><md-option ng-repeat=\"item in vm.tiposIncorporacao\" ng-value=item>{{item.descricao}}</md-option></md-select><span class=form-error ng-show=forms.imovelForm.tipoIncorporacao.$error.required>Campo de preenchimento obrigatório</span></md-input-container><!-- Tipo de Aquisição --><md-input-container class=md-block flex ng-show=vm.isIncorporacaoPorAquisicao()><label>Tipo de Aquisição</label><md-select class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.tipoAquisicao ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=true name=tipoAquisicao ng-class=\"vm.imovel.incorporacao.tipoAquisicao.id !== vm.imovelOriginal.incorporacao.tipoAquisicao.id ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-repeat=\"item in vm.tiposAquisicao\" ng-value=item>{{item.descricao}}</md-option></md-select><span class=form-error ng-show=forms.imovelForm.tipoAquisicao.$error.required>Campo de preenchimento obrigatório</span></md-input-container><!-- Número do Processo --><md-input-container class=md-block flex-gt-xs ng-show=\"imovel.incorporacao.tipoIncorporacao.id === 2\n			&& (vm.imovel.incorporacao.tipoAquisicao.id != 9 && vm.imovel.incorporacao.tipoAquisicao.id != null)\"><label>Número do Processo</label><input class=secaoDadosIncorporacao ng-model=vm.imovel.numeroProcesso name=numeroProcesso md-maxlength=17 ng-required=\"vm.isIncorporacaoPorAquisicao()\n				&& (vm.imovel.incorporacao.tipoAquisicao.id != 9 && vm.imovel.incorporacao.tipoAquisicao.id != null)\" maxlength=17 ng-disabled=vm.isDetail ng-class=\"imovel.numeroProcesso !== imovelOriginal.numeroProcesso ? \'dado-alterado\' : null\"> <span class=form-error ng-show=\"forms.imovelForm.numeroProcesso.$error.required && vm.submittedImovel\">Campo de preenchimento obrigatório</span></md-input-container><!-- Tipo de Contrato Pessoal  --><md-input-container class=md-block flex ng-show=\"vm.imovel.incorporacao.tipoIncorporacao.id === 5\"><label>Tipo de Contrato Pessoal</label><md-select class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.tipoContratoPessoal ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoContrato ng-required=\"vm.imovel.incorporacao.tipoIncorporacao.id === 5\" ng-class=\"vm.imovel.incorporacao.tipoContratoPessoal.id !== vm.imovelOriginal.incorporacao.tipoContratoPessoal.id ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-repeat=\"item in vm.tiposContratoPessoal\" ng-value=item>{{item.descricao}}</md-option></md-select><span class=form-error ng-show=forms.imovelForm.tipoContrato.$error.required>Campo de preenchimento obrigatório</span></md-input-container><!-- Tipo de Fracionamento  --><md-input-container class=md-block flex ng-show=vm.isFormaIncorporacaoFracionamento()><label>Tipo de Fracionamento</label><md-select class=secaoDadosIncorporacao ng-model=vm.imovel.tipoFracionamento ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoFracao ng-required=vm.isIncorporacaoPorDacaoEmPagamento() ng-class=\"vm.imovel.incorporacao.tipoFracionamento.id !== vm.imovelOriginal.incorporacao.tipoFracionamento.id ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-repeat=\"item in vm.tiposFracionamento\" ng-value=item>{{item.descricao}}</md-option></md-select><span class=form-error ng-show=forms.imovelForm.tipoFracao.$error.required>Campo de preenchimento obrigatório</span></md-input-container><!-- mudado de Outro Contrato Pessoal para Descrição do Contrato/Termo  --><md-input-container class=md-block flex ng-show=\"vm.imovel.incorporacao.tipoIncorporacao.id === 5 && vm.imovel.incorporacao.tipoContratoPessoal.id === 6\"><label>Descrição do Contrato/Termo</label><input class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.outroContatoPessoal ng-required=\"vm.imovel.incorporacao.tipoContratoPessoal.id === 6\" name=outroContrato ng-class=\"vm.imovel.incorporacao.outroContatoPessoal !== vm.imovelOriginal.incorporacao.outroContatoPessoal ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail> <span class=form-error ng-show=forms.imovelForm.outroContrato.$error.required>Campo de preenchimento obrigatório</span></md-input-container></div><!-- RIP dos Imóveis Remembrados  --><div layout=column ng-show=\"imovel.incorporacao.tipoIncorporacao.id === 4\"><!-- Tabela --><div class=mgt-25 layout-gt-xs=row><md-content flex-xs=100 flex=100><md-table-container><table md-table md-progress=promise><thead md-head><tr md-row><th md-column>RIP</th><th md-column>Endereço do imóvel</th><th></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"dadosRip in imovel.ripsFusao | limitTo: tabela.limit: (tabela.page - 1) * tabela.limit\"><td md-cell class=inc-text-center>{{dadosRip.rip}}</td><td md-cell class=inc-text-center>{{dadosRip.enderecamento.tipoLogradouro}} {{dadosRip.enderecamento.logradouro}}, Número {{dadosRip.numero}}, {{dadosRip.enderecamento.bairro}}, {{dadosRip.enderecamento.municipio}}/{{dadosRip.enderecamento.uf}}-{{dadosRip.enderecamento.pais.descricao}}. CEP: {{dadosRip.enderecamento.cep}}</td><td md-cell class=inc-text-center><div layout=row layout-align=\"center stretch\"><a target=_blank ui-sref=\"INCORPORACAO.detalharImovel({id: dadosRip.id})\" alt=\"Detalhar RIP\" style=\"margin-top: 8px\"><ng-md-icon icon=search style=fill:#788489><md-tooltip>Detalhar RIP de Origem</md-tooltip></ng-md-icon></a></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tabela.limit md-limit-options=tabela.limitsPage md-page=tabela.page md-total={{imovel.ripsFusao.length}} md-on-paginate=true></md-table-pagination></md-content></div></div><!-- Doação com Encargo --><div layout=row layout-xs=column><md-input-container ng-show=\"vm.imovel.incorporacao.tipoAquisicao.id === 8\"><md-checkbox ng-disabled=vm.isDetail class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.doacaoComEncargo ng-class=\"vm.imovel.incorporacao.doacaoComEncargo !== vm.imovelOriginal.incorporacao.doacaoComEncargo ? \'dado-alterado\' : null\">Doação com Encargo?</md-checkbox></md-input-container></div><!-- Encargo Aquisição --><div layout=row layout-align=\"center center\" ng-show=\"vm.isIncorporacaoPorAquisicao() && vm.imovel.incorporacao.tipoAquisicao.id === 8 && vm.imovel.incorporacao.doacaoComEncargo === true\"><md-input-container class=md-block flex><label>Encargo Aquisição</label><input class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.valorEncargo ui-money-mask ng-required=\"vm.isIncorporacaoPorAquisicao() && vm.imovel.incorporacao.tipoAquisicao.id === 8 && vm.imovel.incorporacao.doacaoComEncargo === true\" name=valorEncargo ng-class=\"vm.imovel.incorporacao.valorEncargo !== vm.imovelOriginal.incorporacao.valorEncargo ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail> <span class=form-error ng-show=forms.imovelForm.valorEncargo.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-datepicker ng-mask=39/19/9999 restrict=reject flex class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.dataFinalEncargo md-placeholder=\"Data Final *\" md-max-date=vm.hoje name=dataEncargo ng-required=\"vm.isIncorporacaoPorAquisicao() && vm.imovel.incorporacao.tipoAquisicao.id === 8 && vm.imovel.incorporacao.doacaoComEncargo === true\" ng-disabled=vm.isDetail></md-datepicker><span class=form-error ng-show=forms.imovelForm.dataEncargo.$error.required>Campo de preenchimento obrigatório</span></div><!-- Fundamento --><div layout=row layout-xs=column><md-input-container class=md-block flex ng-show=\"vm.imovel.incorporacao.tipoIncorporacao.id == 1\" ng-required=\"vm.imovel.incorporacao.tipoIncorporacao.id == 1\"><label>Fundamento</label><md-select multiple class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.fundamento ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=vm.isIncorporacaoPorReversao() name=fundamento ng-class=\"vm.imovel.incorporacao.fundamento.id !== vm.imovelOriginal.incorporacao.fundamento.id ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-repeat=\"item in vm.fundamentos\" ng-value=item>{{item.descricao}}</md-option></md-select><span class=form-error ng-show=forms.imovelForm.fundamento.$error.required>Campo de preenchimento obrigatório</span></md-input-container></div><div layout=row><md-input-container class=md-block flex=50 ng-show=vm.isFormaIncorporacaoFracionamento()><label>RIP de Origem</label><input class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.codigoOrigem ng-disabled=vm.isDetail ng-class=\"vm.imovel.incorporacao.codigoOrigem !== vm.imovelOriginal.incorporacao.codigoOrigem ? \'dado-alterado\' : null\"></md-input-container><div layout=row layout-align=\"center center\" style=\"margin-left: 10px\"><ng-md-icon ng-show=vm.isFormaIncorporacaoFracionamento() icon=search style=fill:#788489><md-tooltip>Detalhar RIP de Origem</md-tooltip></ng-md-icon><a flex ng-show=imovel.incorporacao.codigoOrigem target=_blank ui-sref=\"INCORPORACAO.detalharImovel({id: imovel.incorporacao.codigoOrigem})\">Detalhar RIP de Origem</a></div></div><!-- Código de Origem --><div layout=row layout-xs=column><md-input-container class=md-block flex ng-show=\"vm.isIncorporacaoPorAquisicao() && vm.isIncorporacaoPorEntidadesAPF()\"><label>Código de Origem</label><input class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.codigoOrigem ng-class=\"vm.imovel.incorporacao.codigoOrigem !== vm.imovelOriginal.incorporacao.codigoOrigem ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></md-input-container></div><!-- Situação da Incorporação --><div layout=row layout-xs=column><md-input-container class=md-block flex ng-show=\"vm.imovel.incorporacao.tipoIncorporacao.id !== 5\"><label>Situação da Incorporação</label><md-select class=secaoDadosIncorporacao ng-model=vm.imovel.incorporacao.situacaoIncorporacao ng-model-options=\"{trackBy: \'$value.id\'}\" ng-class=\"vm.imovel.incorporacao.situacaoIncorporacao.id !== vm.imovelOriginal.incorporacao.situacaoIncorporacao.id ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-repeat=\"item in vm.situacoesIncoporacao\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><!-- Direito Adquirido/Incorporado --><div layout=row layout-xs=column><md-input-container class=md-block flex ng-show=\"vm.imovel.incorporacao.tipoIncorporacao.id === 2\"><label>Direito Adquirido/Incorporado</label><md-select ng-model=vm.imovel.incorporacao.direitoIncorporado class=secaoDadosIncorporacao ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=true md-selected=\"valor.id === vm.valorDireitoAdquiridoIncorporado\" ng-disabled=\"vm.valorDireitoAdquiridoIncorporado === 1 || vm.isDetail\" ng-class=\"vm.imovel.incorporacao.direitoIncorporado.id !== vm.imovelOriginal.incorporacao.direitoIncorporado.id ? \'dado-alterado\' : null\"><md-option ng-repeat=\"item in vm.direitosIncorporados\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><!--  Matrícula/Transcrição --><div ng-if=!isTipoImovelCavidadeOuEspelhoDagua ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/fieldsets/matricula-transcricao-fieldset.html\'\"></div><!--  Aqui irá entrar a Matrícula/Transcrição --><!--\n\n		<div layout-gt-xs=\"row\" flex-gt-xs ng-show=\"vm.imovel.tipoImovel.id !== 3 && vm.imovel.tipoImovel.id !== 5\">\n			<md-input-container class=\"md-block\" flex-gt-xs>\n			    <label>Matrícula/Transcrição</label>\n			    <input ng-blur=\"pesquisarImovelMatricula();\" class=\"secaoDadosIncorporacao\" ng-model=\"vm.imovel.incorporacao.numeroMatricula\"\n			    	ng-required=\"vm.matriculaIsRequired\"\n			    	ng-show=\"vm.imovel.tipoImovel !== 3 || vm.imovel.tipoImovel !== 5\"\n			    	name=\"matricula\"\n			    	ng-class=\"vm.imovel.incorporacao.numeroMatricula !== vm.imovelOriginal.incorporacao.numeroMatricula ? \'dado-alterado\' : null\" ng-disabled=\"vm.isDetail\">\n				<span class=\"form-error\" ng-show=\"forms.imovelForm.matricula.$error.required\">Campo de preenchimento obrigatório</span>\n			</md-input-container>\n		</div>\n\n\n	</div>\n\n	<div layout=\"row\" layout-xs=\"column\" ng-show=\"vm.imovel.tipoImovel.id !== 3 && vm.imovel.tipoImovel.id !== 5\">\n		<div layout=\"row\" layout-xs=\"column\">\n			<h5>Data da Matrícula</h5>\n			<md-datepicker ng-mask=\"39/19/9999\" restrict=\"reject\" class=\"secaoDadosIncorporacao\" ng-model=\"vm.imovel.incorporacao.dataMatricula\"\n			 md-placeholder=\"Data da matrícula\"\n		     md-max-date=\"vm.hoje\" ng-required=\"vm.dataMatriculaIsRequired\"\n		     name=\"dataMatricula\" ng-disabled=\"vm.isDetail\"\n		     ng-class=\"vm.imovel.incorporacao.dataMatricula !== vm.imovelOriginal.incorporacao.dataMatricula ? \'dado-alterado\' : null\">\n			</md-datepicker>\n		    <span class=\"form-error\" ng-show=\"forms.imovelForm.dataMatricula.$error.required\">Campo de preenchimento obrigatório</span>\n		</div>\n		<div layout=\"row\" layout-xs=\"column\">\n			<h5 style=\"margin-left: 20px;\">Data de Registro</h5>\n			<md-datepicker ng-mask=\"39/19/9999\" restrict=\"reject\" name=\"dataRegistro\" class=\"secaoDadosIncorporacao\" ng-model=\"vm.imovel.incorporacao.dataRegistro\" md-placeholder=\"Data de registro\" md-max-date=\"vm.hoje\"\n										 ng-class=\"vm.imovel.incorporacao.dataRegistro !== vm.imovelOriginal.incorporacao.dataRegistro ? \'dado-alterado\' : null\" ng-disabled=\"vm.isDetail\">\n			</md-datepicker>\n		</div>\n	</div>\n\n\n	<div layout=\"column\"ng-show=\"vm.imovel.incorporacao.dataMatricula !== undefined && vm.imovel.incorporacao.numeroMatricula !== undefined\">\n\n		<div layout=\"row\" layout-align=\"left end\" layout-wrap ng-if=\"!vm.isDetail\"\n		ng-show=\"vm.imovel.incorporacao.dataMatricula !== undefined && vm.imovel.incorporacao.numeroMatricula !== undefined\">\n			<md-button class=\"md-raised md-primary\"\n				ng-click=\"vm.resetarCertidaoCartorial(); vm.showUploadCertidaoCartorialDialog($event)\"\n				aria-label=\"upload certidão cartorial\">\n				<md-tooltip md-direction=\"bottom\">\n					Documento emitido pelo cartório de registro de imóveis contendo a indicação da matrícula/transcrição e do registro de aquisição em nome da união ou da entidade federal proprietária.\n				</md-tooltip>\n				Adicionar documento(s)\n			</md-button>\n		</div>\n--><!-- Documentos Cartoriais --><div layout=row ng-show=\"imovel.certidoesCartorial.length > 0\"><md-content flex><h4>Documentos cartoriais</h4><table class=mui-table style=\"width:100% !important\"><thead><tr><th style=\"width: 200px !important\">Nome da certidão</th><th>Data da emissão</th><th>Nome do arquivo</th><th>Ação</th></tr></thead><tbody><tr ng-repeat=\"item in imovel.certidoesCartorial\" ng-model-options=\"{trackBy: \'$value.id\'}\" style=\"text-align: center\"><td>{{item.nome}}</td><td>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td>{{item.nomeArquivo}}</td><td><ng-md-icon icon=search style=fill:#788489 size=18 alt=\"Visualizar documento\" ng-click=visualizarCertidao(item.id)></ng-md-icon><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=vm.removerCertidao($index) ng-if=!vm.isDetail></ng-md-icon></td></tr></tbody></table></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/dados-proprietario.tmpl.html","<div><h4>Proprietário</h4><div layout=row layout-xs=column><md-input-container flex-xs=100 flex=25><label>Tipo de Proprietário</label><md-select ng-model=imovel.proprietario.tipoProprietarioReal ng-disabled=\" vm.isOriginalmenteDaUniao() || vm.isImovelDeTerceiros() || vm.isDetail\" ng-change=\"vm.isParcialmente(); vm.verificaUgProprietarioReal()\" ng-model-options=\"{trackBy: \'$value.id\'}\" required name=tipoProprietarioReal ng-required=true><md-option ng-show=!vm.naoExibeTerceirosProprietarioReal(proprietario) ng-repeat=\"proprietario in vm.tiposProprietarioReal\" ng-value=proprietario>{{proprietario.descricao}}</md-option></md-select></md-input-container><md-input-container ng-if=vm.isTipoProprietarioAutarquiaFundacao() ng-class=\"forms.proprietarioForm.orgaoEntidadeProprietarioReal.$error.required && submittedProprietario ?\'md-input-invalid\': null\" flex-xs=100 flex=25><label>Órgão/Entidade</label><md-select class=secaoDadosProprietario ng-disabled=vm.isDetail ng-model=vm.orgaoEntidadeProprietario ng-model-options=\"{trackBy: \'$value.id\'}\" name=orgaoEntidadeProprietarioReal required><md-option ng-repeat=\"item in vm.orgaosEntidades\" ng-value=item>{{item.nome}}</md-option></md-select></md-input-container><md-input-container ng-if=vm.isTipoProprietarioAutarquiaFundacao() ng-class=\"forms.proprietarioForm.ugProprietarioReal.$error.required && submittedProprietario ?\'md-input-invalid\': null\" flex-xs=100 flex=25><label>UG Específica</label><md-select class=secaoDadosProprietario ng-disabled=vm.isDetail ng-model=vm.ugProprietario ng-model-options=\"{trackBy: \'$value.id\'}\" name=ugProprietarioReal required><md-option ng-repeat=\"item in vm.ugsReal\" ng-value=item>{{item.id}} - {{item.nome}}</md-option></md-select></md-input-container></div><div ng-if=\"(!vm.isImovelDeTerceiros()) && imovel.proprietario.tipoProprietarioReal.id\"><label>Proprietário é:</label><div layout=row layout-xs=column><md-radio-group flex-xs=100 flex=50 style=\"margin-top: 20px\" ng-class=\"forms.proprietarioForm.isIntegralmente.$error.required && submittedProprietario ?\'md-input-invalid\': null\" ng-model=imovel.proprietario.isIntegralmente name=isIntegralmente ng-change=vm.isParcialmente() required ng-disabled=\"(idImovelOrigem && imovel.incorporacao.tipoIncorporacao.id === 3) || vm.isTipoImovelCavidadeOuEspelhoDagua() || vm.isDetail\"><md-radio-button id=radioUniaoIntegralmente value=true style=\"float: left !important\" ng-checked=true>Integralmente</md-radio-button><md-radio-button id=radioUniaoParcialmente value=false style=\"float: left; margin-left:20px; margin-top: 0 !important\">Parcialmente</md-radio-button></md-radio-group><md-input-container ng-if=vm.isParcialmente() ng-class=\"forms.proprietarioForm.proprietarioParcial.$error.required && submittedImovel? \'md-input-invalid\': null\" class=md-block flex-xs=100 flex=25><label>Proprietário Parcial</label><md-select class=secaoDadosProprietario ng-model=imovel.proprietario.tipoProprietarioParcial ng-model-options=\"{trackBy: \'$value.id\'}\" name=proprietarioParcial required ng-disabled=\"(idImovelOrigem && imovel.incorporacao.tipoIncorporacao.id === 3) || vm.isDetail\"><md-option ng-repeat=\"item in vm.tiposProprietarioParcial\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div></div><div layout=row layout-xs=column ng-if=vm.isTipoProprietarioParcialAutarquiaFundacao()><md-input-container ng-class=\"forms.proprietarioForm.tipoProprietarioParcial.$error.required && submittedProprietario ?\'md-input-invalid\': null\" flex-xs=100 flex=50><label>Órgão/Entidade</label><md-select class=secaoDadosProprietario ng-model=vm.orgaoEntidadeProprietarioParcial ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"(idImovelOrigem && imovel.incorporacao.tipoIncorporacao.id === 3) || vm.isDetail\" name=tipoProprietarioParcial ng-change=vm.buscaUgsProprietarioParcial() required><md-option ng-repeat=\"item in vm.orgaosEntidades\" ng-value=item>{{item.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block ng-class=\"forms.proprietarioForm.ugProprietarioParcial.$error.required && submittedProprietario ?\'md-input-invalid\': null\" ng-if=\"vm.isTipoProprietarioParcialAutarquiaFundacao() && vm.orgaoEntidadeProprietarioParcial.id && vm.ugs.length > 0\" flex-xs=100 flex=25><label>UG Específica</label><md-select class=secaoDadosProprietario ng-model=vm.ugProprietarioParcial ng-change=vm.setDadosUgParcial() ng-model-options=\"{trackBy: \'$value.id\'}\" required ng-disabled=vm.isDetail name=ugProprietarioParcial><md-option ng-repeat=\"item in vm.ugs\" ng-value=item>{{item.id}} - {{item.nome}}</md-option></md-select></md-input-container></div><!-- Terceiros --><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/fieldsets/terceiros-proprietario-fieldset.html\'\"></div><div><h4>Titular do direito real ou pessoal</h4><div layout-gt-xs=row layout-xs=column><md-input-container ng-class=\"forms.proprietarioForm.tipoProprietarioTitular.$error.required && submittedProprietario? \'md-input-invalid\' : null\" class=md-block flex-xs=100 flex-gt-sm=25><label>Tipo Titular</label><md-select ng-disabled=\"(idImovelOrigem && imovel.incorporacao.tipoIncorporacao.id === 3) || vm.isDetail\" class=secaoDadosProprietario ng-model=imovel.proprietarioTitular.tipoProprietarioReal ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoProprietarioTitular required><md-option ng-show=\"!vm.naoExibeTerceirosTitular(item) \" ng-repeat=\"item in vm.tiposTitular\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container flex-xs=100 flex=50 ng-class=\"forms.proprietarioForm.orgaoEntidadeProprietarioTitular.$error.required && submittedProprietario? \'md-input-invalid\' : null\" ng-if=vm.isTipoTitularAutarquiaFundacao()><label>Órgão/Entidade</label><md-select class=secaoDadosProprietario ng-model=vm.orgaoEntidadeTitular ng-change=vm.buscaUgsProprietarioTitular() ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"(idImovelOrigem && imovel.incorporacao.tipoIncorporacao.id === 3) || vm.isDetail\" name=orgaoEntidadeProprietarioTitular required><md-option ng-repeat=\"item in vm.orgaosEntidades\" ng-value=item>{{item.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block ng-if=\"vm.isTipoTitularAutarquiaFundacao() && vm.orgaoEntidadeTitular.id && vm.ugsTitular.length > 0\" ng-class=\"forms.proprietarioForm.ugProprietarioTitular.$error.required && submittedProprietario? \'md-input-invalid\' : null\" flex-xs=100 flex=50><label>UG Específica</label><md-select class=secaoDadosProprietario ng-model=vm.ugProprietarioTitular ng-change=vm.setDadosUgTitular() ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=vm.isDetail name=ugProprietarioTitular required><md-option ng-repeat=\"item in vm.ugsTitular\" ng-value=item>{{item.id}} - {{item.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column ng-if=vm.isTipoTitularUniao()><div layout=column><md-input-container ng-class=\"forms.proprietarioForm.cnpjOrgaoTitular.$error.required && submittedProprietario? \'md-input-invalid md-input-focused\': null\" class=md-block style=\"height: 15%\"><label>CNPJ do Órgão:</label><input ng-change=vm.validaCnpjProprietarioTitular() ui-br-cnpj-mask class=secaoDadosProprietario ng-model=imovel.proprietarioTitular.cnpjAdministracaoIndireta ng-disabled=\"(idImovelOrigem && imovel.incorporacao.tipoIncorporacao.id === 3) || vm.isDetail\" required name=cnpjOrgaoTitular></md-input-container><div layout-gt-xs=row>Nome: {{imovel.proprietarioTitular.nomeReceita}}</div></div></div></div><div ng-if=vm.isAquisicao()><h4>Proprietário Anterior</h4><div layout=row layout-xs=column><md-input-container ng-if=!vm.isAquisicaoPorSucessaoPorEntidadesdaAPF() flex-xs=100 flex=25><label>Tipo de Proprietário</label><md-select class=secaoDadosProprietario ng-model=imovel.proprietarioAnterior.tipoProprietarioReal ng-model-options=\"{trackBy: \'$value.id\'}\" ng-disabled=\"(idImovelOrigem && imovel.incorporacao.tipoIncorporacao.id === 3) || vm.isDetail\" name=tipoProprietario><md-option ng-show=!vm.naoExibeUniaoProprietarioAnterior(item) ng-repeat=\"item in vm.tiposProprietarioAnterior\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-if=vm.isTipoProprietarioAnteriorAutarquiaFundacao() flex-xs=100 flex=25><label>Órgão/Entidade</label><md-select class=secaoDadosProprietario ng-model=vm.orgaoEntidadeAnterior ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"\" ng-disabled=vm.isDetail ng-change=vm.buscaUgsProprietarioAnterior()><md-option ng-repeat=\"item in vm.orgaosEntidades\" ng-value=item>{{item.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block ng-if=\"vm.isTipoProprietarioAnteriorAutarquiaFundacao() && vm.orgaoEntidadeAnterior.id && vm.ugsAnterior.length > 0\" flex-xs=100 flex=25><label>UG Específica</label><md-select class=secaoDadosProprietario ng-disabled=vm.isDetail ng-model=vm.ugProprietarioAnterior ng-change=vm.verificaUgProprietarioReal() ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"\"><md-option ng-repeat=\"item in vm.ugsAnterior\" ng-value=item>{{item.id}} - {{item.nome}}</md-option></md-select></md-input-container></div><md-input-container ng-if=vm.isAquisicaoPorSucessaoPorEntidadesdaAPF() ng-class=\"forms.proprietarioForm.entiExtinta.$error.required && submittedProprietario? \'md-input-invalid\' : null\" class=md-block flex><label>Órgão/Entidade</label><md-autocomplete class=secaoDadosProprietario ng-required=vm.isAquisicaoPorSucessaoPorEntidadesdaAPF() md-input-name=entiExtinta md-no-cache=true md-items=\"item in vm.querySearch(vm.searchText)\" md-selected-item=imovel.proprietarioAnterior.entidadeExtinta ng-disabled=vm.isDetail md-search-text=vm.searchText md-selected-item-change=vm.selectedItemChange(item) required md-item-text=item.display><md-item-template><span md-highlight-text=vm.searchText>{{item.display}}</span></md-item-template></md-autocomplete></md-input-container><!-- Terceiros --><div ng-include=\"\'scripts-cadastro-imovel/pages/imovel/view/fieldsets/terceiros-proprietario-anterior-fieldset.html\'\"></div></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/dados-unidade-autonoma.tmpl.html","<div layout=column><div layout=row layout-xs=column><md-input-container><label>Área construída</label><input class=secaoDadosUA ng-model=imovel.unidadeAutonoma.areaConstruida ui-number-mask required name=areaConstruida ng-class=\"imovel.unidadeAutonoma.areaConstruida !== imovelOriginal.unidadeAutonoma.areaConstruida ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs><label>Memorial Descritivo</label><textarea class=secaoDadosUA ng-model=imovel.unidadeAutonoma.memorialDescritivo md-maxlength=2000 ng-class=\"imovel.unidadeAutonoma.memorialDescritivo !== imovelOriginal.unidadeAutonoma.memorialDescritivo ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block ng-show=isApartamento() flex><label>Número de dormitórios</label><input class=secaoDadosUA ng-model=imovel.unidadeAutonoma.numeroDormitorios type=number ng-class=\"imovel.unidadeAutonoma.numeroDormitorios !== imovelOriginal.unidadeAutonoma.numeroDormitorios ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></md-input-container><md-input-container flex><label>Padrão de acabamento</label><md-select class=secaoDadosUA ng-model=imovel.unidadeAutonoma.padraoAcabamento ng-model-options=\"{trackBy : $value.id}\" name=padraoAcabamento required ng-class=\"imovel.unidadeAutonoma.padraoAcabamento !== imovelOriginal.unidadeAutonoma.padraoAcabamento ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-repeat=\"item in vm.tiposAcabamento\" ng-value=item ng-selected=\"item.id === imovel.unidadeAutonoma.padraoAcabamento.id\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container flex><label>Estado de conservação</label><md-select class=secaoDadosUA ng-model=imovel.unidadeAutonoma.estadoConservacao ng-model-options=\"{trackBy : $value.id}\" name=estadoConservacao required ng-class=\"imovel.unidadeAutonoma.estadoConservacao.id !== imovelOriginal.unidadeAutonoma.estadoConservacao.id ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-repeat=\"item in vm.estadosConservacao\" ng-value=item ng-selected=\"item.id === imovel.unidadeAutonoma.estadoConservacao.id\">{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container style=\"margin-right: 25px\"><md-checkbox class=secaoDadosUA ng-model=imovel.unidadeAutonoma.possuiGaragem ng-class=\"imovel.unidadeAutonoma.possuiGaragem !== imovelOriginal.unidadeAutonoma.possuiGaragem ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail>Possui garagem</md-checkbox></md-input-container><md-input-container ng-show=imovel.unidadeAutonoma.possuiGaragem ng-required=imovel.unidadeAutonoma.possuiGaragem><md-checkbox name=garagemMatricula class=secaoDadosUA ng-model=imovel.unidadeAutonoma.garagemComMatricula ng-class=\"imovel.unidadeAutonoma.garagemComMatricula !== imovelOriginal.unidadeAutonoma.garagemComMatricula ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail>Garagem com matrícula</md-checkbox></md-input-container></div><div layout=row ng-show=imovel.unidadeAutonoma.possuiGaragem><md-input-container flex ng-required=imovel.unidadeAutonoma.possuiGaragem><label>Tipo de garagem</label><md-select class=secaoDadosUA ng-model=imovel.unidadeAutonoma.tipoGaragemUA ng-model-options=\"{trackBy : $value.id}\" name=tipoGaragemUA required ng-class=\"imovel.unidadeAutonoma.tipoGaragemUA !== imovelOriginal.unidadeAutonoma.tipoGaragemUA ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-value=1 ng-selected=\"imovel.unidadeAutonoma.tipoGaragemUA === 1\">Coberta</md-option><md-option ng-value=2 ng-selected=\"imovel.unidadeAutonoma.tipoGaragemUA === 2\">Descoberta</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs ng-required=imovel.unidadeAutonoma.possuiGaragem><label>Quantidade de vagas</label><input name=numeroVagas class=secaoDadosUA ng-model=imovel.unidadeAutonoma.numeroVagas type=number ng-class=\"imovel.unidadeAutonoma.numeroVagas !== imovelOriginal.unidadeAutonoma.numeroVagas ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs><label>Observações gerais</label><textarea class=secaoDadosUA ng-model=imovel.unidadeAutonoma.observacoes md-maxlength=2000 ng-class=\"imovel.unidadeAutonoma.observacoes !== imovelOriginal.unidadeAutonoma.observacoes ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></textarea></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/descricao-cadastral.tmpl.html","<div layout=column><div layout-gt-xs=row style=\"margin-bottom: 10px\"><md-radio-group ng-model=imovel.terreno.tipoDesenhoCadastral style=\"float: left; margin-top: 10px\"><md-radio-button id=radio1 ng-value=1 ng-click=terrenoCtrl.alterarParaArquivoShapeFile($event) style=float:left ng-disabled=vm.isDetail>Arquivo (Shapefile)</md-radio-button><md-radio-button id=radio2 ng-value=2 ng-click=terrenoCtrl.alterarParaVetorizacaoEmTela($event) style=\"float:left; margin-top: 0; margin-left: 15px !important\" ng-disabled=vm.isDetail>Vetorização em Tela</md-radio-button></md-radio-group></div><section layout=row layout-sm=column layout-align=\"right center\" layout-wrap ng-hide=vm.isDetail><md-button class=\"md-raised md-primary\" ng-click=\"terrenoCtrl.arquivo.arquivo ? terrenoCtrl.showShapeFileCadastralJaEnviado($event) : terrenoCtrl.showUploadArquivoTerrenoDialog($event)\" ng-disabled=\"imovel.terreno.tipoDesenhoCadastral === 2\">Escolher Arquivo</md-button>{{vm.imovel.terreno.arquivoCadastral.nome}}<ng-md-icon ng-hide=!imovel.terreno.arquivoCadastral.nome icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=terrenoCtrl.excluirArquivoTerrenoCadastral()></ng-md-icon></section><md-input-container class=md-block flex-gt-xs><label>Memorial Descritivo</label><textarea ng-disabled=\"terrenoCtrl.desabilitarPreenchimentoMemorialDescritivo || vm.isDetail\" class=secaoDadosDescricaoCadastral ng-model=imovel.terreno.memorialDescritivoCadastral ng-required=\"imovel.terreno.geometriaTerreno && !imovel.incorporacao.numeroMatricula\" md-maxlength=2000 name=memorialDescritivoCadastral ng-class=\"imovel.terreno.memorialDescritivoCadastral !== imovelOriginal.terreno.memorialDescritivoCadastral ? \'dado-alterado\' : null\"></textarea><span class=form-error ng-show=forms.terrenoForm.memorialDescritivoCadastral.$error.required>Campo de preenchimento obrigatório</span></md-input-container><div layout=row layout-xs=column><md-input-container class=md-block flex=25><label style=\"text-align: center\">Unidade de Medida da Área:</label></md-input-container><md-input-container class=md-block flex><md-radio-group name=unidadeMedidaCadastral style=\"float: left !important\" layout=row layout-xs=column ng-model=vm.imovel.terreno.unidadeMedidaCadastral ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral()><md-radio-button id=radioUnidadeMedidaM value=m2 ng-disabled=vm.isDetail>m²</md-radio-button><md-radio-button id=radioUnidadeMedidaHa value=ha ng-disabled=vm.isDetail>ha</md-radio-button></md-radio-group></md-input-container></div><div layout-gt-xs=row style=\"height: 50px\"><md-input-container class=md-block flex-gt-xs><label>Área Total (m²)</label><input type=text class=secaoDadosDescricaoCadastral ng-model=imovel.terreno.areaTotalCadastral ng-required=imovel.terreno.geometriaTerreno name=areaTotalCadastral ui-number-mask=2 name=areaTotalCadastral ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral(); ng-blur=terrenoCtrl.validaFracaoIdealImovelCadastral(); ng-class=\"imovel.terreno.areaTotalCadastral !== imovelOriginal.terreno.areaTotalCadastral ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail> <span class=form-error ng-show=forms.terrenoForm.areaTotalCadastral.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-input-container class=md-block flex-gt-xs ng-show=imovel.proprietario.isIntegralmente><label>Área da União(m²)</label><input type=text ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral(); class=secaoDadosDescricaoCadastral ng-model=imovel.terreno.areaUniaoCadastral ui-number-mask=2 ng-required=\"imovel.terreno.geometriaTerreno && imovel.proprietario.isIntegralmente\" ng-blur=terrenoCtrl.validaFracaoIdealImovelCadastral(); name=areaUniaoCadastral ng-class=\"imovel.terreno.areaUniaoCadastral !== imovelOriginal.terreno.areaUniaoCadastral ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><div class=md-block ng-show=\"vm.imovel.terreno.unidadeMedidaCadastral == \'ha\' &&\n				vm.imovel.terreno.areaUniaoCadastral\"></div><span class=form-error ng-show=\"forms.terrenoForm.areaUniaoCadastral.$error.required && submittedTerreno\">Campo de preenchimento obrigatório</span></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs ng-show=\"imovel.terreno.unidadeMedidaCadastral == \'ha\' &&\n								imovel.terreno.areaTotalCadastral != undefined\" style=\"margin-top: 0px\">{{imovel.terreno.areaTotalCadastral*10000|number:2|numeroBr}} m²</md-input-container></div><div layout=row layout-xs=column ng-show=\"imovel.terreno.unidadeMedidaCadastral == \'ha\' && imovel.terreno.areaTotalCadastral != undefined\"><md-input-container style=\"margin-top: 0px\" class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerreno &&\n							imovel.proprietario.isIntegralmente &&\n							imovel.terreno.unidadeMedidaCadastral == \'ha\' &&\n							imovel.terreno.areaUniaoCadastral != undefined\">{{imovel.terreno.areaUniaoCadastral*10000|number:2|numeroBr}} m²</md-input-container></div><div layout-gt-xs=row><md-input-container class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4\"><label>Fração Ideal do Imóvel(m²)</label><input type=text name=fracaoIdealImovelCadastral id=fracaoIdealImovelCadastral ng-change=terrenoCtrl.calcularFracaoIdealUniaoCadastral(); class=secaoDadosDescricaoCadastral ng-model=\"imovel.unidadeAutonoma.fracaoIdealCadastral \" ng-required=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4\" ng-blur=terrenoCtrl.validaFracaoIdealImovelCadastral(); ui-number-mask name=fracaoIdealImovelCadastral ng-disabled=vm.isDetail> <span class=form-error ng-show=\"forms.terrenoForm.fracaoIdealImovelCadastral.$error.required && submittedTerreno\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container class=md-block flex-gt-xs ng-show=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4 && imovel.proprietario.isIntegralmente\"><label style=\"margin-left: 90px !important\">Fração Ideal da União(m²)</label></md-input-container><md-input-container class=md-block flex-xs ng-show=\"imovel.terreno.geometriaTerreno && imovel.tipoImovel.id === 4 && imovel.proprietario.isIntegralmente\" style=\"margin-right: 300px !important\"><label>{{imovel.terreno.fracaoIdealUniaoCadastral}}</label></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs ng-show=\"vm.imovel.terreno.geometriaTerreno && vm.imovel.tipoImovel.id === 4 && vm.imovel.terreno.unidadeMedidaCadastral == \'ha\'\" style=\"margin-top: 0px\">{{vm.imovel.unidadeAutonoma.fracaoIdealCadastral *10000}} m²</md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/descricao-cartorial.tmpl.html","<div layout=column><section layout=row layout-sm=column layout-align=\"right center\" layout-wrap ng-hide=vm.isDetail><md-button class=\"md-raised md-primary\" ng-click=\"terrenoCtrl.arquivoCartorial.arquivo ? terrenoCtrl.showShapeFileCartorialJaEnviado($event) : terrenoCtrl.showUploadArquivoTerrenoDialogCartorial($event)\">Escolher Arquivo</md-button>{{imovel.terreno.arquivoCartorial.nome}}<ng-md-icon ng-hide=!imovel.terreno.arquivoCartorial.nome icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=terrenoCtrl.excluirArquivoTerrenoCadastral(terrenoCtrl.arquivoCartorial)></ng-md-icon></section><md-checkbox class=secaoDadosDescricaoCartorial ng-model=imovel.terreno.isCertificaoIncra ng-show=vm.showCertificadoIncra ng-class=\"imovel.terreno.isCertificaoIncra !== imovelOriginal.terreno.isCertificaoIncra ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail>Levantamento cadastral certificado pelo Incra</md-checkbox><md-input-container class=md-block flex-gt-xs><label>Memorial Descritivo</label><textarea class=secaoDadosDescricaoCartorial ng-model=imovel.terreno.memorialDescritivoCartorial ng-required=\"imovel.terreno.geometriaTerrenoCartorial && imovel.tipoImovel.id !== 5 && imovel.tipoImovel.tipoPai !== \'UNIDADE AUTÔNOMA\'\" name=memorialDescritivoCartorial ng-class=\"imovel.terreno.memorialDescritivoCartorial !== imovelOriginal.terreno.memorialDescritivoCartorial ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></textarea><span class=form-error ng-show=forms.terrenoForm.memorialDescritivoCartorial.$error.required>Campo de preenchimento obrigatório</span></md-input-container><div layout=row layout-xs=column><md-input-container class=md-block flex=25><label style=\"text-align: center\">Unidade de Medida da Área:</label></md-input-container><md-input-container class=md-block flex><md-radio-group name=unidadeMedidaCartorial style=\"float: left !important\" layout=row layout-xs=column ng-model=vm.imovel.terreno.unidadeMedidaCartorial ng-change=terrenoCtrl.calcularFracaoIdealUniaoCartorial()><md-radio-button id=radioUnidadeMedidaM value=m2 ng-disabled=vm.isDetail>m²</md-radio-button><md-radio-button id=radioUnidadeMedidaHa value=ha ng-disabled=vm.isDetail>ha</md-radio-button></md-radio-group></md-input-container></div><div layout-gt-xs=row><md-input-container class=md-block flex-gt-xs ng-show=imovel.terreno.geometriaTerrenoCartorial><label>Área Total (m²)</label><input type=text class=secaoDadosDescricaoCartorial ng-model=imovel.terreno.areaTotalCartorial ng-required=imovel.terreno.geometriaTerrenoCartorial ui-number-mask name=areaTotalCartorial ng-class=\"imovel.terreno.areaTotalCartorial !== imovelOriginal.terreno.areaTotalCartorial ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail> <span class=form-error ng-show=forms.terrenoForm.areaTotalCartorial.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-input-container class=md-block flex-gt-xs ng-show=imovel.proprietario.isIntegralmente><label>Área da União(m²)</label><input type=text class=secaoDadosDescricaoCartorial ng-model=imovel.terreno.areaUniaoCartorial ng-required=\"imovel.terreno.geometriaTerrenoCartorial && imovel.proprietario.isIntegralmente\" ui-number-mask name=areaUniaoCartorial ng-class=\"imovel.terreno.areaUniaoCartorial !== imovelOriginal.terreno.areaUniaoCartorial ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail> <span class=form-error ng-show=forms.terrenoForm.areaUnicaoCartorial.$error.required>Campo de preenchimento obrigatório</span></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs ng-show=\"vm.imovel.terreno.unidadeMedidaCartorial == \'ha\'\" style=\"margin-top: 0px\">{{vm.imovel.terreno.areaTotalCartorial*10000}} m²</md-input-container><md-input-container class=md-block flex-gt-xs ng-show=\"vm.imovel.terreno.unidadeMedidaCartorial == \'ha\'\" style=\"margin-top: 0px\">{{vm.imovel.terreno.areaUniaoCartorial*10000}} m²</md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/detalhes-imovel.tmpl.html","<div layout=column><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs><label>Natureza do Imóvel</label><md-select class=secaoDetalhesImovel ng-model=vm.imovel.naturezaImovel ng-model-options=\"{trackBy: \'$value.id\'}\" required name=natureza ng-class=\"vm.imovel.naturezaImovel.id !== vm.imovelOriginal.naturezaImovel.id ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail><md-option ng-repeat=\"item in vm.naturezasImoveis\" ng-value=item>{{item.descricao}}</md-option></md-select><span class=form-error ng-show=forms.imovelForm.natureza.$error.required>Campo de preenchimento obrigatório</span></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block ng-show=\"vm.imovel.naturezaImovel.id === 1\" flex=20><label>Nº Inscrição Municipal</label><input class=secaoDetalhesImovel ng-model=vm.imovel.numeroInscricaoMunicipal ng-class=\"vm.imovel.numeroInscricaoMunicipal !== vm.imovelOriginal.numeroInscricaoMunicipal ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></md-input-container><md-input-container class=md-block ng-show=\"vm.imovel.naturezaImovel.id === 2\" flex=20><label>Nº Inscrição Rural</label><input class=secaoDetalhesImovel ng-model=vm.imovel.numeroInscricaoRural ng-class=\"vm.imovel.numeroInscricaoRural !== vm.imovelOriginal.numeroInscricaoRural ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex><label>Observações Gerais</label><textarea class=secaoDetalhesImovel ng-model=vm.imovel.observacoesGerais md-maxlength=500 ng-class=\"vm.imovel.observacoesGerais !== vm.imovelOriginal.observacoesGerais ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail></textarea></md-input-container></div><div layout=row><md-input-container><md-checkbox class=secaoDetalhesImovel ng-model=vm.imovel.ilha ng-class=\"vm.imovel.ilha !== vm.imovelOriginal.ilha ? \'dado-alterado\' : null\" ng-disabled=vm.isDetail>Imóvel está contido em uma Ilha?</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block ng-show=imovel.ilha flex><label>Tipo de ilha</label><md-select class=secaoDetalhesImovel ng-model=vm.imovel.tipoIlha ng-model-options=\"{trackBy: \'$value.id\'}\" ng-required=vm.imovel.ilha name=tipoIlha ng-disabled=vm.isDetail ng-class=\"vm.imovel.tipoIlha !== vm.imovelOriginal.tipoIlha ? \'dado-alterado\' : null\"><md-option ng-repeat=\"item in vm.tiposIlhas\" ng-value=item>{{item.descricao}}</md-option></md-select><span class=form-error ng-show=forms.imovelForm.tipoIlha.$error.required>Campo de preenchimento obrigatório</span></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/documentacao-unidade-autonoma.tmpl.html","<div layout=column><div layout=row><md-input-container><md-checkbox class=secaoDadosDocumentacao ng-model=vm.imovel.unidadeAutonoma.possuiPlanoProtecaoIncendio aria-label=\"Plano de proteção contra incêndio\" ng-disabled=vm.isDetail>Plano de proteção contra incêndio</md-checkbox></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacao\" ngf-select ng-model=vm.imovel.unidadeAutonoma.planoProtecaoIncendio style=\"margin: -4px 0 0 15px\" ng-change=\"file2blob(\'planoProtecaoIncendioFile\', vm.imovel.unidadeAutonoma.planoProtecaoIncendio)\" type=file ng-disabled=\"!vm.imovel.unidadeAutonoma.possuiPlanoProtecaoIncendio || vm.isDetail\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-required=\"vm.imovel.unidadeAutonoma.possuiPlanoProtecaoIncendio && vm.imovel.unidadeAutonoma.planoProtecaoIncendio === undefined\" name=planoProtecaoIncendioFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.planoProtecaoIncendioFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"vm.imovel.unidadeAutonoma.planoProtecaoIncendio != undefined && !vm.isDetail\"><ng-md-icon ng-click=\"vm.imovel.unidadeAutonoma.planoProtecaoIncendio = undefined\" class=icon-delete icon=delete></ng-md-icon></md-input-container></div><div layout=row><md-input-container><md-checkbox class=secaoDadosDocumentacao ng-model=vm.imovel.unidadeAutonoma.possuiHabitese aria-label=Habite-se ng-disabled=vm.isDetail>Habite-se</md-checkbox></md-input-container><md-input-container ng-show=imovel.unidadeAutonoma.possuiHabitese style=\"margin: 12px 0 0 20px\"><label>Ano:</label><input class=secaoDadosDocumentacao ng-model=vm.imovel.unidadeAutonoma.anoHabitese type=number maxlength=4 ng-disabled=vm.isDetail></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacao\" ngf-select ng-model=vm.imovel.unidadeAutonoma.habitese type=file style=\"margin: -4px 0 0 15px\" ng-change=\"file2blob(\'habiteseFile\', vm.imovel.unidadeAutonoma.habitese)\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-disabled=\"!vm.imovel.unidadeAutonoma.possuiHabitese || vm.isDetail\" ng-required=\"vm.imovel.unidadeAutonoma.possuiHabitese && vm.imovel.unidadeAutonoma.habitese === undefined\" name=habiteseFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.habiteseFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span><md-input-container><md-input-container ng-show=\"vm.imovel.unidadeAutonoma.habitese != undefined && !vm.isDetail\"><ng-md-icon ng-click=\"vm.imovel.unidadeAutonoma.habitese = undefined\" class=icon-delete icon=delete></ng-md-icon></md-input-container></md-input-container></md-input-container></div><div layout=row><md-input-container><md-checkbox class=secaoDadosDocumentacao ng-model=vm.imovel.unidadeAutonoma.possuiAlvaraConstrucao aria-label=\"Alvará de construção/reforma\" ng-disabled=vm.isDetail>Alvará de construção/reforma</md-checkbox></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacao\" ngf-select ng-model=vm.imovel.unidadeAutonoma.alvaraConstrucao ng-change=\"file2blob(\'alvaraConstrucaoFile\', vm.imovel.unidadeAutonoma.alvaraConstrucao)\" type=file style=\"margin: -4px 0 0 15px\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-disabled=\"!vm.imovel.unidadeAutonoma.possuiAlvaraConstrucao || vm.isDetail\" ng-required=\"vm.imovel.unidadeAutonoma.possuiAlvaraConstrucao && vm.imovel.unidadeAutonoma.alvaraConstrucao === undefined\" name=alvaraConstrucaoFile> <span class=form-error ng-show=adicionarUnidadeAutonomaForm.alvaraConstrucaoFile.$error.required>Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"vm.imovel.unidadeAutonoma.alvaraConstrucao != undefined && !vm.isDetail\"><ng-md-icon ng-click=\"vm.imovel.unidadeAutonoma.alvaraConstrucao = undefined\" class=icon-delete icon=delete></ng-md-icon></md-input-container></div><div layout=row><md-input-container><md-checkbox class=secaoDadosDocumentacao ng-model=vm.imovel.unidadeAutonoma.possuiLaudoPredial aria-label=\"Laudo de inspeção predial\" ng-disabled=vm.isDetail>Laudo de inspeção predial</md-checkbox></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacao\" ngf-select ng-model=vm.imovel.unidadeAutonoma.laudoPredial ng-change=\"file2blob(\'laudoPredialFile\', vm.imovel.unidadeAutonoma.laudoPredial)\" type=file accept=.pdf,.img,.jpg,.jpeg style=\"margin: -4px 0 0 15px\" ngf-max-size=10MB ng-disabled=\"!vm.imovel.unidadeAutonoma.possuiLaudoPredial || vm.isDetail\" ng-required=\"vm.imovel.unidadeAutonoma.possuiLaudoPredial && vm.imovel.unidadeAutonoma.laudoPredial === undefined\" name=laudoPredialFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.laudoPredialFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span><md-input-container><md-input-container ng-show=\"vm.imovel.unidadeAutonoma.laudoPredial != undefined && !vm.isDetail\"><ng-md-icon ng-click=\"vm.imovel.unidadeAutonoma.laudoPredial = undefined\" class=icon-delete icon=delete></ng-md-icon></md-input-container></md-input-container></md-input-container></div><div layout-gt-xs=row><md-input-container><label>Outro:</label><textarea class=secaoDadosDocumentacao ng-model=vm.imovel.unidadeAutonoma.outroDescricao ng-disabled=vm.isDetail></textarea></md-input-container><md-input-container><input class=\"md-raised secaoDadosDocumentacao\" style=\"margin: -4px 0 0 15px\" ngf-select ng-model=vm.imovel.unidadeAutonoma.outro ng-change=\"file2blob(\'outroFile\', vm.imovel.unidadeAutonoma.outro)\" type=file ng-disabled=\"vm.imovel.unidadeAutonoma.outroDescricao === undefined || vm.isDetail\" accept=.pdf,.img,.jpg,.jpeg ngf-max-size=10MB ng-required=\"vm.imovel.unidadeAutonoma.outroDescricao !== undefined && vm.imovel.unidadeAutonoma.outro === undefined\" name=outroDescricaoFile> <span class=form-error ng-show=\"forms.adicionarUnidadeAutonomaForm.outroDescricaoFile.$error.required && submittedUnidadeAutonoma\">Campo de preenchimento obrigatório</span></md-input-container><md-input-container ng-show=\"vm.imovel.unidadeAutonoma.outro != undefined && !vm.isDetail\"><ng-md-icon ng-click=\"vm.imovel.unidadeAutonoma.outro = undefined\" class=icon-delete icon=delete></ng-md-icon></md-input-container></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/imovel/view/detalhar-imovel/componentes/planta-unidade.tmpl.html","<div layout=column><div layout=row><md-button class=\"md-raised md-primary\" ng-click=\"vm.resetarPlantaUnidade(); vm.showUploadPlantaUnidadeDialog($event)\" aria-label=\"upload planta unidade\" ng-hide=vm.isDetail>Adicionar plantas da unidade</md-button></div><div layout=row ng-show=\"vm.imovel.unidadeAutonoma.plantasUnidade.length > 0\"><md-content style=\"width: 100% !important\"><h4>Plantas da unidade autônoma</h4><table class=mui-table style=\"width:100% !important\"><thead><tr><th style=\"width: 200px !important\">Nome da certidão</th><th>Data da emissão</th><th>Nome do arquivo</th><th>Ação</th></tr></thead><tbody><tr ng-repeat=\"item in vm.imovel.unidadeAutonoma.plantasUnidade\" ng-model-options=\"{trackBy: \'$value.id\'}\"><td>{{item.nome}}</td><td>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td>{{item.nomeArquivo}}</td><td><ng-md-icon icon=search style=fill:#788489 size=18 alt=\"Visualizar documento\" ng-click=vm.visualizarPlanta(item.id)></ng-md-icon><ng-md-icon icon=delete style=fill:#788489 size=18 alt=Excluir ng-click=vm.removerPlanta($index) ng-hide=vm.isDetail></ng-md-icon></td></tr></tbody></table></md-content></div></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-benfeitoria/condicoes-de-acessibilidade.tmpl.html","<md-table-container ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><table md-table flex border=2px class=tabela-acessibilidade><thead><tr md-row><th colspan=4>Condições de Acessibilidade</th></tr><tr md-row><th style=\"width: 60%\">Campo</th><th style=\"width: 16%\">Original</th><th style=\"width: 16%\">Alterado/Novo</th><th style=\"width: 8%\">Recusar</th></tr></thead><tbody><tr md-row><td md-cell>Há vagas próximas dos acessos de circulação de pedestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</td><td md-cell><span ng-show=imovelOriginal.exibir><span ng-show=item.imovelOriginal.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelOriginal.edificacao.vagasSinalizadas.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaCondicoesAcessibilidadeAlterado(item.imovelOriginal.edificacao.vagasSinalizadas.descricao, item.imovelAlterado.edificacao.vagasSinalizadas.descricao, item)\"><span ng-show=item.imovelAlterado.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelAlterado.edificacao.vagasSinalizadas.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"isDesabilitarCheckboxAcessibilidade(item.imovelOriginal.edificacao.vagasSinalizadas.descricao, item.imovelAlterado.edificacao.vagasSinalizadas.descricao, item)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.vagasSinalizadas ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row><td md-cell>Os acessos ao interior da edificação estão livres de barreiras arquitetónicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=imovelOriginal.exibir><span ng-show=item.imovelOriginal.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelOriginal.edificacao.acessoLivreObstaculos.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaCondicoesAcessibilidadeAlterado(item.imovelOriginal.edificacao.acessoLivreObstaculos.descricao, item.imovelAlterado.edificacao.acessoLivreObstaculos.descricao, item)\">{{item.imovelAlterado.edificacao.acessoLivreObstaculos.descricao}} <span ng-show=item.imovelAlterado.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade.</span></td><td md-cell><md-checkbox ng-disabled=\"isDesabilitarCheckboxAcessibilidade(item.imovelOriginal.edificacao.acessoLivreObstaculos.descricao, item.imovelAlterado.edificacao.acessoLivreObstaculos.descricao, item)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.acessoLivreObstaculos ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row><td md-cell>Há banheiro acessível, com seus equipamentos e acessórios distribuidos de maneira que possam ser utilizafos por pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=item.imovelOriginal.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.banheiroAcessivel.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaCondicoesAcessibilidadeAlterado(item.imovelOriginal.edificacao.banheiroAcessivel.descricao, item.imovelAlterado.edificacao.banheiroAcessivel.descricao, item)\"><span ng-show=item.imovelAlterado.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelAlterado.edificacao.banheiroAcessivel.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"isDesabilitarCheckboxAcessibilidade(item.imovelOriginal.edificacao.banheiroAcessivel.descricao, item.imovelAlterado.edificacao.banheiroAcessivel.descricao, item)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.banheiroAcessivel ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row><td md-cell>Os elevadores oferecem condições de acessibilidade para pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=imovelOriginal.exibir><span ng-show=item.imovelOriginal.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelOriginal.edificacao.elevadorAcessivel.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaCondicoesAcessibilidadeAlterado(item.imovelOriginal.edificacao.elevadorAcessivel.descricao, item.imovelAlterado.edificacao.elevadorAcessivel.descricao, item)\"><span ng-show=item.imovelAlterado.edificacao.semInformacaoAcessibilidade>Sem informações de acessibilidade. </span>{{item.imovelAlterado.edificacao.elevadorAcessivel.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"isDesabilitarCheckboxAcessibilidade(item.imovelOriginal.edificacao.elevadorAcessivel.descricao, item.imovelAlterado.edificacao.elevadorAcessivel.descricao, item)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.elevadorAcessivel ng-true-value=true ng-false-value=false></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-benfeitoria/dados-acessao-benfeitoria.tmpl.html","<md-table-container><table md-table flex border=2px class=tabela-dados><thead><tr md-row><th colspan=4>Dados da Acessão/Benfeitoria</th></tr><tr md-row ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/detalhar-imovel-label-cabecalho.tmpl.html\'\"></tr></thead><tbody><tr md-row><td md-cell>Tipo de Acessão / Benfeitoria</td><td md-cell><span ng-show=imovelOriginal.exibir><span ng-show=item.imovelOriginal.edificacao>Edificação / {{item.imovelOriginal.edificacao.tipoEspecializacaoEdificacao.descricao}} </span><span ng-show=item.imovelOriginal.benfeitoriaComplementar>Complementar / {{item.imovelOriginal.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao}}</span></span></td><td md-cell ng-class=\"alterouTipoBenfeitoria(item) ? \'dado-alterado\' : null\"><span ng-show=item.imovelAlterado.edificacao ng-class=\"item.imovelOriginal.edificacao ? null : \'dado-alterado\'\">Edificação / {{item.imovelAlterado.edificacao.tipoEspecializacaoEdificacao.descricao}} </span><span ng-show=item.imovelAlterado.benfeitoriaComplementar ng-class=\"item.imovelAlterado.benfeitoriaComplementar ? null : \'dado-alterado\'\">Complementar / {{item.imovelAlterado.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao}}</span></td><td md-cell><md-checkbox ng-disabled=\"imovelOriginal.id !== imovelAlterado.id && !alterouTipoBenfeitoria(item)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.tipoBenfeitoria ng-true-value=null ng-false-value={}></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Nome</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.nome}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.nome, item.imovelAlterado.nome, item)\">{{item.imovelAlterado.nome}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.nome, item.imovelAlterado.nome)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.nome ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row><td md-cell>Área Construída</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.areaConstruida}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.areaConstruida, item.imovelAlterado.areaConstruida, item)\">{{item.imovelAlterado.areaConstruida}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.areaConstruida, item.imovelAlterado.areaConstruida)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.areaConstruida ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Área de Projeção</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.areaProjecao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.areaProjecao, item.imovelAlterado.edificacao.areaProjecao, item)\">{{item.imovelAlterado.edificacao.areaProjecao}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.areaProjecao, item.imovelAlterado.edificacao.areaProjecao)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.areaProjecao ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><!--alterado de taxa de ocupação para Fração de Ocupação--><td md-cell>Fração de Ocupação</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.taxaOcupacao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.taxaOcupacao, item.imovelAlterado.edificacao.taxaOcupacao, item)\">{{item.imovelAlterado.edificacao.taxaOcupacao}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.taxaOcupacao, item.imovelAlterado.edificacao.taxaOcupacao)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.taxaOcupacao ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Índice de Aproveitamento</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.indiceAproveitamento}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.indiceAproveitamento, item.imovelAlterado.edificacao.indiceAproveitamento, item)\">{{item.imovelAlterado.edificacao.indiceAproveitamento}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.indiceAproveitamento, item.imovelAlterado.edificacao.indiceAproveitamento)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.indiceAproveitamento ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Número de Pavimentos</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.numeroPavimentos}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.numeroPavimentos, item.imovelAlterado.edificacao.numeroPavimentos, item)\">{{item.imovelAlterado.edificacao.numeroPavimentos}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.numeroPavimentos, item.imovelAlterado.edificacao.numeroPavimentos)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.numeroPavimentos ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Número de Dormitórios</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.numeroDormitorios}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.numeroDormitorios, item.imovelAlterado.edificacao.numeroDormitorios, item)\">{{item.imovelAlterado.edificacao.numeroDormitorios}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.numeroDormitorios, item.imovelAlterado.edificacao.numeroDormitorios)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.numeroDormitorios ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Número de Unidades Residenciais</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.numeroUnidadesResidenciais}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.numeroUnidadesResidenciais, item.imovelAlterado.edificacao.numeroUnidadesResidenciais, item)\">{{item.imovelAlterado.edificacao.numeroUnidadesResidenciais}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.numeroUnidadesResidenciais, item.imovelAlterado.edificacao.numeroUnidadesResidenciais)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.numeroUnidadesResidenciais ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Averbada na Matrícula do Imóvel</td><td md-cell><span ng-show=imovelOriginal.exibir>{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.averbada)}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.averbada, item.imovelAlterado.edificacao.averbada, item)\">{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.averbada)}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.averbada), homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.averbada))\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.averbada ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Possui Habite-se</td><td md-cell><span ng-show=imovelOriginal.exibir>{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiHabitese)}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.possuiHabitese, item.imovelAlterado.edificacao.possuiHabitese, item)\">{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiHabitese)}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiHabitese), homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiHabitese))\" aria-label=dadosBenfeitoria aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.possuiHabitese ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Ano</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.anoHabitese}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.anoHabitese, item.imovelAlterado.edificacao.anoHabitese, item)\">{{item.imovelAlterado.edificacao.anoHabitese}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.anoHabitese, item.imovelAlterado.edificacao.anoHabitese)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.anoHabitese ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Possui Garagem</td><td md-cell><span ng-show=imovelOriginal.exibir>{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiGaragem)}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.possuiGaragem, item.imovelAlterado.edificacao.possuiGaragem, item)\">{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiGaragem)}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiGaragem), homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiGaragem))\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.possuiGaragem ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Número de Vagas</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.numeroVagas}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.numeroVagas, item.imovelAlterado.edificacao.numeroVagas, item)\">{{item.imovelAlterado.edificacao.numeroVagas}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.numeroVagas, item.imovelAlterado.edificacao.numeroVagas)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.numeroVagas ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Possui Elevador</td><td md-cell><span ng-show=imovelOriginal.exibir>{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiElevador)}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.possuiElevador, item.imovelAlterado.edificacao.possuiElevador, item)\">{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiElevador)}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiElevador), homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiElevador))\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.possuiElevador ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Possui Etiqueta Nacional de Conservação de Energia (ENCE) Geral</td><td md-cell><span ng-show=imovelOriginal.exibir>{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiENCE)}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.possuiENCE, item.imovelAlterado.edificacao.possuiENCE, item)\">{{homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiENCE)}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelOriginal.edificacao.possuiENCE), homologarImvlCtrl.boolToStrinCheckboxProprerty(item.imovelAlterado.edificacao.possuiENCE))\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.possuiENCE ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.benfeitoriaComplementar || item.imovelAlterado.benfeitoriaComplementar\"><td md-cell>Estado de Conservação</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.estadoConservacao.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.estadoConservacao.descricao, item.imovelAlterado.estadoConservacao.descricao, item)\">{{item.imovelAlterado.estadoConservacao.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.estadoConservacao.descricao, item.imovelAlterado.estadoConservacao.descricao)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.estadoConservacaoBenfeitoria ng-true-value=null ng-false-value=null></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Tipo de Estrutura</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.tipoEstrutura.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.tipoEstrutura.descricao, item.imovelAlterado.edificacao.tipoEstrutura.descricao, item)\">{{item.imovelAlterado.edificacao.tipoEstrutura.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.tipoEstrutura.descricao, item.imovelAlterado.edificacao.tipoEstrutura.descricao)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.tipoEstrutura ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row ng-show=\"item.imovelOriginal.edificacao || item.imovelAlterado.edificacao\"><td md-cell>Padrão de Acabamento</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.edificacao.padraoAcabamento.descricao}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.edificacao.padraoAcabamento.descricao, item.imovelAlterado.edificacao.padraoAcabamento.descricao, item)\">{{item.imovelAlterado.edificacao.padraoAcabamento.descricao}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.edificacao.padraoAcabamento.descricao, item.imovelAlterado.edificacao.padraoAcabamento.descricao)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.padraoAcabamento ng-true-value=true ng-false-value=false></md-checkbox></td></tr><tr md-row><td md-cell>Observações Gerais</td><td md-cell><span ng-show=imovelOriginal.exibir>{{item.imovelOriginal.observacoes}}</span></td><td md-cell ng-class=\"valorBenfeitoriaAlterado(item.imovelOriginal.observacoes, item.imovelAlterado.observacoes, item)\">{{item.imovelAlterado.observacoes}}</td><td md-cell><md-checkbox ng-disabled=\"desabilitarCheckbox(item.imovelOriginal.observacoes, item.imovelAlterado.observacoes)\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=imovelRecusado.observacoes ng-true-value=true ng-false-value=false></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-benfeitoria/detalhar-imovel-dados-benfeitoria-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-show=\"item.benfeitoriaOriginal.tipoBenfeitoria != null\"><label class=label-component>Tipo de Acessão/Benfeitoria:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.tipoBenfeitoria.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.tipoProjeto != null\"><label class=label-component>Tipo de Projeto:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.tipoProjeto.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.nome != null\"><label class=label-component>Nome:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.nome }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.areaConstruida != null\"><label class=label-component>Área Construída:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.areaConstruida }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.areaProjecao != null\"><label class=label-component>Área de Projeção:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.areaProjecao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.taxaOcupacao != null\"><label class=label-component>Taxa de Ocupação:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.taxaOcupacao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.indiceAproveitamento != null\"><label class=label-component>Índice de Aproveitamento:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.indiceAproveitamento }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.numeroPavimentos != null\"><label class=label-component>Número de Pavimentos:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.numeroPavimentos }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.numeroDormitorios != null\"><label class=label-component>Número de Domitórios:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.numeroDormitorios }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.averbada != null\"><label class=label-component>Averbada na Matrícula do Imóvel:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.averbada? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.descricaoAverbacao != null\"><label class=label-component>Descrição da Averbação:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.descricaoAverbacao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.possuiGaragem != null\"><label class=label-component>Possui Garagem:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.possuiGaragem }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.numeroVagasCobertas != null\"><label class=label-component>Número de vagas cobertas:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.numeroVagasCobertas }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.numeroVagasDescobertas != null\"><label class=label-component>Número de vagas descobertas:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.numeroVagasDescobertas }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.possuiElevador != null\"><label class=label-component>Possui Elevador:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.possuiElevador? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.possuiENCE != null\"><label class=label-component>Possui Etiqueta Nac. de Cons. de Energ. Geral:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.possuiENCE? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.tipoEnce != null\"><label class=label-component>Tipo:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.tipoEnce.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.tipoEnceParcial != null\"><label class=label-component>Tipo de Parcial:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.tipoEnceParcial.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.nivelEnce != null\"><label class=label-component>Nível:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.nivelEnce.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaOriginal.edificacao.observacoes != null\"><label class=label-component>Observações Gerais:</label><span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.observacoes }}</span></span></td><td md-cell><div id=benfeitoria{{$index}}><span ng-show=\"item.benfeitoriaAlterada.tipoBenfeitoria != null\"><label class=label-component>Tipo de Acessão/Benfeitoria:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.tipoBenfeitoria.descricao, item.benfeitoriaAlterada.tipoBenfeitoria.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.tipoBenfeitoria.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.tipoProjeto != null\"><label class=label-component>Tipo de Projeto:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.tipoProjeto.descricao, item.benfeitoriaAlterada.edificacao.tipoProjeto.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.tipoProjeto.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.nome != null\"><label class=label-component>Nome:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.nome, item.benfeitoriaAlterada.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.nome }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.areaConstruida != null\"><label class=label-component>Área Construída:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.areaConstruida, item.benfeitoriaAlterada.areaConstruida) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.areaConstruida }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.areaProjecao != null\"><label class=label-component>Área de Projeção:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.areaProjecao, item.benfeitoriaAlterada.edificacao.areaProjecao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.areaProjecao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.taxaOcupacao != null\"><label class=label-component>Taxa de Ocupação:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.taxaOcupacao, item.benfeitoriaAlterada.edificacao.taxaOcupacao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.taxaOcupacao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.indiceAproveitamento != null\"><label class=label-component>Índice de Aproveitamento:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.indiceAproveitamento, item.benfeitoriaAlterada.edificacao.indiceAproveitamento) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.indiceAproveitamento }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.numeroPavimentos != null\"><label class=label-component>Número de Pavimentos:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.numeroPavimentos, item.benfeitoriaAlterada.edificacao.numeroPavimentos) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.numeroPavimentos }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.numeroDormitorios != null\"><label class=label-component>Número de Domitórios:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.numeroDormitorios, item.benfeitoriaAlterada.edificacao.numeroDormitorios) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.numeroDormitorios }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.averbada != null\"><label class=label-component>Averbada na Matrícula do Imóvel:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.averbada, item.benfeitoriaAlterada.edificacao.averbada) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.averbada? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.descricaoAverbacao != null\"><label class=label-component>Descrição da Averbação:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.descricaoAverbacao, item.benfeitoriaAlterada.edificacao.descricaoAverbacao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.descricaoAverbacao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.possuiGaragem != null\"><label class=label-component>Possui Garagem:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.possuiGaragem, item.benfeitoriaAlterada.edificacao.possuiGaragem) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.possuiGaragem }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.numeroVagasCobertas != null\"><label class=label-component>Número de vagas cobertas:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.numeroVagasCobertas, item.benfeitoriaAlterada.edificacao.numeroVagasCobertas) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.numeroVagasCobertas }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.numeroVagasDescobertas != null\"><label class=label-component>Número de vagas descobertas:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.numeroVagasDescobertas, item.benfeitoriaAlterada.edificacao.numeroVagasDescobertas) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.numeroVagasDescobertas }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.possuiElevador != null\"><label class=label-component>Possui Elevador:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.possuiElevador, item.benfeitoriaAlterada.edificacao.possuiElevador) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.possuiElevador? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.possuiENCE != null\"><label class=label-component>Possui Etiqueta Nac. de Cons. de Energ. Geral:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.possuiENCE, item.benfeitoriaAlterada.edificacao.possuiENCE) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.possuiENCE? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.tipoEnce.descricao != null\"><label class=label-component>Tipo:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.tipoEnce.descricao, item.benfeitoriaAlterada.edificacao.tipoEnce.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.tipoEnce.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.tipoEnceParcial != null\"><label class=label-component>Tipo de Parcial:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.tipoEnceParcial.descricao, item.benfeitoriaAlterada.edificacao.tipoEnceParcial.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.tipoEnceParcial.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.nivelEnce != null\"><label class=label-component>Nível:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.nivelEnce.descricao, item.benfeitoriaAlterada.edificacao.nivelEnce.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.nivelEnce.descricao }}</span><br></span><span ng-show=\"item.benfeitoriaAlterada.edificacao.observacoes != null\"><label class=label-component>Observações Gerais:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.observacoes, item.benfeitoriaAlterada.edificacao.observacoes) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.observacoes }}</span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar benfeitoria {{item.benfeitoriaAlterada.codigo}}\" name=benfeitoria_{{item.benfeitoriaAlterada.codigo}} ng-checked=\"homCtrl.exists(\'benfeitoria_\' + item.benfeitoriaAlterada.codigo)\" ng-disabled=\"homCtrl.isDifferent(\'#benfeitoria\'+ {{$index}})\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Condições de Acessibilidade</label><br><label class=label-component>Há vagas próximas dos acessos de circulação de pedestres devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação\" : item.benfeitoriaOriginal.edificacao.vagasSinalizadas.descricao}}</span><br><label class=label-component>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.acessoLivreObstaculos.descricao}}</span><br><label class=label-component>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.banheiroAcessivel.descricao}}</span><br><label class=label-component>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ item.benfeitoriaOriginal.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao}}</span></td><td md-cell><div id=benfeitoriaAcessibilidade{{$index}}><label class=label-titulo>Condições de Acessibilidade</label><br><label class=label-component>Há vagas próximas dos acessos de circulação de pedestres devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.vagasSinalizadas.descricao, item.benfeitoriaAlterada.edificacao.vagasSinalizadas.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.vagasSinalizadas.descricao}}</span><br><label class=label-component>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.acessoLivreObstaculos.descricao, item.benfeitoriaAlterada.edificacao.acessoLivreObstaculos.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.acessoLivreObstaculos.descricao}}</span><br><label class=label-component>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.banheiroAcessivel.descricao, item.benfeitoriaAlterada.edificacao.banheiroAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.banheiroAcessivel.descricao}}</span><br><label class=label-component>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.elevadorAcessivel.descricao, item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.edificacao.semInformacaoAcessibilidade? \"Sem Informação.\" : item.benfeitoriaAlterada.edificacao.elevadorAcessivel.descricao}}</span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Acessibilidade Benfeitoria {{item.benfeitoriaAlterada.codigo}}\" name=benfeitoriaAcessibilidade_{{item.benfeitoriaAlterada.codigo}} ng-checked=\"homCtrl.exists(\'benfeitoriaAcessibilidade_\' + item.benfeitoriaAlterada.codigo)\" ng-disabled=\"homCtrl.isDifferent(\'#benfeitoriaAcessibilidade\'+ {{$index}})\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Documentação</label><span ng-repeat=\"itemOriginal in item.benfeitoriaOriginal.edificacao.documentacao\"><br><span ng-show=\"itemOriginal.nome != null\"><label class=label-component>Documento:</label><span class=span-original-component>{{ itemOriginal.nome }}</span></span><br><span ng-show=\"itemOriginal.dataEmissao != null\"><label class=label-component>Data de Emissão:</label><span class=span-original-component>{{ itemOriginal.dataEmissao | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.dataValidade != null\"><label class=label-component>Data de Validade:</label><span class=span-original-component>{{ itemOriginal.dataValidade | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.arquivo != null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.arquivo.url }}\" download=\"{{ itemOriginal.arquivo.nomeOriginal }}\">{{ itemOriginal.arquivo.nomeOriginal }}</a></span><br></span></span></td><td md-cell><div id=benfeitoriaDocumentacao{{$index}}><label class=label-titulo>Documentação</label><span ng-repeat=\"itemAlterado in item.benfeitoriaAlterada.edificacao.documentacao\"><br><span ng-show=\"itemAlterado.nome != null\"><label class=label-component>Documento:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.documentacao[$index].nome, itemAlterado.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.nome }}</span><br></span><span ng-show=\"itemAlterado.dataEmissao  != null\"><label class=label-component>Data de Emissão:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.documentacao[$index].dataEmissao, itemAlterado.dataEmissao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataEmissao | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.dataValidade != null\"><label class=label-component>Data de Validade:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.documentacao[$index].dataValidade, itemAlterado.dataValidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataValidade | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.arquivo != null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.edificacao.documentacao[$index].arquivo.nome, itemAlterado.arquivo.nome) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.arquivo.url }}\" download=\"{{ itemAlterado.arquivo.nomeOriginal }}\">{{ itemAlterado.arquivo.nomeOriginal }}</a></span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Documentação Benfeitoria {{item.benfeitoriaAlterada.codigo}}\" name=benfeitoriaDocumentacao_{{item.benfeitoriaAlterada.codigo}} ng-checked=\"homCtrl.exists(\'benfeitoriaDocumentacao_\' + item.benfeitoriaAlterada.codigo)\" ng-disabled=\"homCtrl.isDifferent(\'#benfeitoriaDocumentacao\'+ {{$index}})\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Plantas da Benfeitoria</label><span ng-repeat=\"itemOriginal in item.benfeitoriaOriginal.plantas\"><br><span ng-show=\"itemOriginal.descricao != null\"><label class=label-component>Descrição:</label><span class=span-original-component>{{ itemOriginal.descricao }}</span><br></span><span ng-show=\"itemOriginal.data != null\"><label class=label-component>Data:</label><span class=span-original-component>{{ itemOriginal.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.nomeArquivo != null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.url }}\" download=\"{{ itemOriginal.nomeArquivo }}\">{{ itemOriginal.nomeArquivo }}</a></span><br></span></span></td><td md-cell><div id=benfeitoriaPlantas{{$index}}><label class=label-titulo>Plantas da Benfeitoria</label><span ng-repeat=\"itemAlterado in item.benfeitoriaAlterada.plantas\"><br><span ng-show=\"itemAlterado.descricao != null\"><label class=label-component>Descrição:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.plantas[$index].descricao, itemAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.descricao }}</span><br></span><span ng-show=\"itemAlterado.data != null\"><label class=label-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.plantas[$index].data, itemAlterado.data) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.nomeArquivo != null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.plantas[$index].nomeArquivo, itemAlterado.nomeArquivo) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.url }}\" download=\"{{ itemAlterado.nomeArquivo }}\">{{ itemAlterado.nomeArquivo }}</a></span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Plantas Benfeitoria {{item.benfeitoriaAlterada.codigo}}\" name=benfeitoriaPlantas_{{item.benfeitoriaAlterada.codigo}} ng-checked=\"homCtrl.exists(\'benfeitoriaPlantas_\' + item.benfeitoriaAlterada.codigo)\" ng-disabled=\"homCtrl.isDifferent(\'#benfeitoriaPlantas\'+ {{$index}})\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><div style=\"margin-top: 5px\"><label class=label-titulo>Fotos da Benfeitoria</label><br><span><inc-modal-fotos titulo-modal=\"Fotos da Benfeitoria\" fotos=item.benfeitoriaOriginal.imagens></inc-modal-fotos></span></div></td><td md-cell><div style=\"margin-top: 5px\"><div id=benfeitoriaFotos{{$index}}><label class=label-titulo style=\"margin-top: 5px\">Fotos da Benfeitoria</label><br><span ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.imagens , item.benfeitoriaAlterada.imagens) ? \'span-alterado-component\' : \'span-original-component\'\"><inc-modal-fotos titulo-modal=\"Fotos da Benfeitoria\" fotos=item.benfeitoriaAlterada.imagens></inc-modal-fotos><span ng-hide=true ng-class=\"homCtrl.isDadoDivergente(item.benfeitoriaOriginal.imagens , item.benfeitoriaAlterada.imagens) ? \'span-alterado-component\' : \'span-original-component\'\">{{ item.benfeitoriaAlterada.imagens }}</span></span></div></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Fotos Benfeitoria {{item.benfeitoriaAlterada.codigo}}\" name=benfeitoriaFotos_{{item.benfeitoriaAlterada.codigo}} ng-checked=\"homCtrl.exists(\'benfeitoriaFotos_\' + item.benfeitoriaAlterada.codigo)\" ng-disabled=\"homCtrl.isDifferent(\'#benfeitoriaFotos\'+ {{$index}})\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-benfeitoria/detalhar-imovel-dados-benfeitoria.tmpl.html","<style>.valorCondicoesAcessibilidade{\n	width: 100px !important; padding-right:20px !important;\n}</style><div layout=column><md-table-container><table md-table flex border=2px class=tabela-mapa><thead><tr md-row><th colspan=3>Localização da Benfeitoria</th></tr><tr md-row><th class=labelMapa>Original</th><th class=labelMapa>Alterado/Novo</th><th class=labelRecusar>Recusar</th></tr></thead><tbody><tr md-row><td md-cell style=\"width: 46% !important\"><div layout=row>&nbsp;&nbsp;&nbsp;&nbsp;<div layout=column class=cellMapa><ci-mapa-incorporacao ng-if=homologarImvlCtrl.imovelOriginal showminimapa=false tabindex=\"\" imovel=homologarImvlCtrl.imovelOriginal action=detalharImovelHomologar habilitar-desenho=false></ci-mapa-incorporacao></div>&nbsp;&nbsp;</div></td></tr></tbody></table></md-table-container></div><td md-cell style=\"width: 46% !important\"><div layout=row>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<div layout=column class=cellMapa><ci-mapa-incorporacao ng-if=homologarImvlCtrl.imovelAlterado showminimapa=false tabindex=\"\" imovel=homologarImvlCtrl.imovelAlterado action=detalharImovelHomologar habilitar-desenho=false></ci-mapa-incorporacao></div></div></td><td md-cell class=checkbox-mapa><md-checkbox ng-disabled=\"imovelAlterado.id !== imovelOriginal.id && item.desabilitarCheckboxMapa\" aria-label=dadosBenfeitoria ng-change=change(false) ng-model=homologarImvlCtrl.imovelRecusado.localizacaoImovel></md-checkbox></td><tr md-row><td md-cell class=cell-fotos><span ng-show=\"imovelAlterado.id !== imovelOriginal.id\"><a href=\"\">Ver Fotos da Benfeitoria({{item.imovelOriginal.imagens.length}})</a></span></td><td md-cell class=cell-fotos><a href=\"\">Ver Fotos da Benfeitoria({{item.imovelAlterado.imagens.length}})</a></td><td md-cell class=checkbox-mapa><md-checkbox ng-disabled=true aria-label=dadosBenfeitoria ng-change=change(false) ng-model=homologarImvlCtrl.imovelRecusado.fotosBenfeitoria></md-checkbox></td></tr><div layout=column ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/plantas-da-benfeitoria.tmpl.html\'\"></div><div layout=column ng-include=\"scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/dados-acessao-benfeitoria.tmpl.html\'\"></div><div layout=column ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/condicoes-de-acessibilidade.tmpl.html\'\"></div><div layout=column ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/componentes-benfeitoria/documentacao-benfeitoria.tmpl.html\'\"></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-benfeitoria/documentacao-benfeitoria.tmpl.html","<md-table-container><table md-table flex border=2px class=tabela-dados><thead><tr md-row><th colspan=4>Documentação</th></tr><tr md-row ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/detalhar-imovel-label-cabecalho.tmpl.html\'\"></tr></thead><tbody><tr md-row><td md-cell>Documentos</td><td md-cell><span ng-show=imovelOriginal.exibir ng-repeat=\"planta in item.imovelOriginal.plantas\"><a href=\"\" ng-click=openPdfPlanta(planta)></a><br></span></td><td md-cell><span ng-repeat=\"planta in item.imovelAlterado.plantas\"><a href=\"\" ng-click=openPdfPlanta(planta)></a><br></span></td><td md-cell><md-checkbox ng-disabled=true ng-change=change(false) ng-model=homologarImvlCtrl.imovelRecusado.plantasBenfeitoria aria-label=dadosBenfeitoria></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-benfeitoria/plantas-da-benfeitoria.tmpl.html","<md-table-container><table md-table flex border=2px class=tabela-dados><thead><tr md-row><th colspan=4>Plantas da Benfeitoria</th></tr><tr md-row ng-include=\"\'scripts-cadastro-imovel/pages/homologar/homologar-mapa/view/detalhar-imovel-label-cabecalho.tmpl.html\'\"></tr></thead><tbody><tr md-row><td md-cell>Plantas</td><td md-cell><span ng-show=imovelOriginal.exibir ng-repeat=\"planta in item.imovelOriginal.plantas\"><a href=\"\" ng-click=openPdfPlanta(planta)>{{planta.nomeArquivo}}</a><br></span></td><td md-cell ng-class=\"getEstiloArquivosBenfeitoria(item.imovelOriginal.plantas, item.imovelAlterado.plantas)\"><span ng-repeat=\"planta in item.imovelAlterado.plantas\"><a href=\"\" ng-click=openPdfPlanta(planta) ng-class=\"getEstiloArquivosBenfeitoria(item.imovelOriginal.plantas, item.imovelAlterado.plantas)\">{{planta.nomeArquivo}}</a><br></span></td><td md-cell><md-checkbox ng-disabled=\"!isArrayArquivosModificado(item.imovelOriginal.plantas, item.imovelAlterado.plantas)\" ng-change=change(false) ng-model=homologarImvlCtrl.imovelRecusado.plantasBenfeitoria aria-label=dadosBenfeitoria></md-checkbox></td></tr></tbody></table></md-table-container>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-dados-gerais-imovel/dados-do-proprietario-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Dados do Proprietário Titular</label><br><span ng-show=\"homCtrl.imovelOriginal.proprietario.tipoProprietarioReal !== null\"><label class=label-component>Tipo Proprietário:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.descricao === \'União\'\"><label class=label-component>CNPJ da União:</label><span class=span-original-component>00.489.828/0009-02</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietariosCartorio !== null\"><label class=label-component>Possui outro proprietário registrado em cartório:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietariosCartorio.length !== 0 ? \"Sim\" : \"Não\" }}</span><br></span><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.proprietariosCartorio\"><span ng-show=\"itemOriginal.nome !== null\"><label class=label-sub-component>Nome:</label><span class=span-original-component>{{ itemOriginal.nome }}</span><br></span><span ng-show=\"itemOriginal.cpf !== null\"><label class=label-sub-component>CPF/CNPJ:</label><span class=span-original-component>{{ itemOriginal.cpf }}</span><br></span></span><span ng-show=\"homCtrl.imovelOriginal.proprietario.orgaoEntidade !== null\"><label class=label-component>Órgão/Entidade:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietario.orgaoEntidade }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietario.codigoUg !== null\"><label class=label-component>UG Específica:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietario.codigoUg }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.cnpjProprietario !== null && homCtrl.imovelOriginal.cnpjProprietario !== \'00.489.828/0009-02\'\"><label class=label-component>CNPJ da UG:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.cnpjProprietario }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietariosTerceiros.length > 0\"><label class=label-component>Terceiros:</label><br></span><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.proprietariosTerceiros\"><span ng-show=\"itemOriginal.nome !== null\"><label class=label-sub-component>Nome:</label><span class=span-original-component>{{ itemOriginal.nome }}</span><br></span><span ng-show=\"itemOriginal.cpf !== null\"><label class=label-sub-component>CPF/CNPJ:</label><span class=span-original-component>{{ itemOriginal.cpf }}</span><br></span></span><span ng-show=\"homCtrl.imovelOriginal.proprietario.codigoOrgaoEntidadeLocatario !== null\"><label class=label-component>Órgão/Entidade Locatária:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietario.codigoOrgaoEntidadeLocatario }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietario.codigoUgLocatario !== null\"><label class=label-component>UG Específica Locatária:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietario.codigoUgLocatario }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.cnpjUgEspecificaLocatario !== null\"><label class=label-component>CNPJ da UG Locatária:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.cnpjUgEspecificaLocatario }}</span><br></span></td><td md-cell><div id=proprietarioTitular><label class=label-titulo>Dados do Proprietário Titular</label><br><span ng-show=\"homCtrl.imovelAlterado.proprietario.tipoProprietarioReal !== null\"><label class=label-component>Tipo Proprietário:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao === \'União\'\"><label class=label-component>CNPJ da União:</label><span class=span-original-component>00.489.828/0009-02</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietariosCartorio !== null\"><label class=label-component>Possui outro proprietário registrado em cartório:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietariosCartorio, homCtrl.imovelAlterado.proprietariosCartorio) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietariosCartorio.length !== 0 ? \"Sim\" : \"Não\" }}</span><br></span><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.proprietariosCartorio\"><span ng-show=\"itemAlterado.nome !== null\"><label class=label-sub-component>Nome:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietariosCartorio[$index].nome, itemAlterado.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.nome }}</span><br></span><span ng-show=\"itemAlterado.cpf !== null\"><label class=label-sub-component>CPF/CNPJ:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietariosCartorio[$index].cpf, itemAlterado.cpf) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.cpf }}</span><br></span></span><span ng-show=\"homCtrl.imovelAlterado.proprietario.orgaoEntidade !== null\"><label class=label-component>Órgão/Entidade:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.orgaoEntidade, homCtrl.imovelAlterado.proprietario.orgaoEntidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietario.orgaoEntidade }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietario.codigoUg !== null\"><label class=label-component>UG Específica:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.codigoUg, homCtrl.imovelAlterado.proprietario.codigoUg) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietario.codigoUg }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.cnpjProprietario !== null && homCtrl.imovelAlterado.cnpjProprietario !== \'00.489.828/0009-02\'\"><label class=label-component>CNPJ da UG:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.codigoUg, homCtrl.imovelAlterado.proprietario.codigoUg) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.cnpjProprietario }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietariosTerceiros.length > 0\"><label class=label-component>Terceiros:</label><br></span><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.proprietariosTerceiros\"><span ng-show=\"itemAlterado.nome !== null\"><label class=label-sub-component>Nome:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietariosTerceiros[$index].nome, itemAlterado.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.nome }}</span><br></span><span ng-show=\"itemAlterado.cpf !== null\"><label class=label-sub-component>CPF/CNPJ:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietariosTerceiros[$index].cpf, itemAlterado.cpf) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.cpf }}</span><br></span></span><span ng-show=\"homCtrl.imovelAlterado.proprietario.codigoOrgaoEntidadeLocatario !== null\"><label class=label-component>Órgão/Entidade Locatária:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.codigoOrgaoEntidadeLocatario, homCtrl.imovelAlterado.proprietario.codigoOrgaoEntidadeLocatario) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietario.codigoOrgaoEntidadeLocatario }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietario.codigoUgLocatario !== null\"><label class=label-component>UG Específica Locatária:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.codigoUgLocatario, homCtrl.imovelAlterado.proprietario.codigoUgLocatario) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietario.codigoUgLocatario }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.cnpjUgEspecificaLocatario !== null\"><label class=label-component>CNPJ da UG Locatária:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.cnpjUgEspecificaLocatario, homCtrl.imovelAlterado.cnpjUgEspecificaLocatario) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.cnpjUgEspecificaLocatario }}</span><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar dados de Incorporação\" name=proprietarioTitular ng-model=homCtrl.informacoesRecusada.proprietarioTitular ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.PROPRIETARIO_TITULAR)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior !== null || homCtrl.imovelAlterado.proprietarioAnterior !== null\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Proprietário Anterior</label><br><span ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal !== null\"><label class=label-component>Tipo Proprietário:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade !== null\"><label class=label-component>Órgão/Entidade:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior.codigoUg !== null\"><label class=label-component>UG Específica:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietarioAnterior.codigoUg }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj !== null\"><label class=label-component>Não possui CPF/CNPJ:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.proprietarioAnterior.justificativa !== null\"><label class=label-component>Justificativa:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.proprietarioAnterior.justificativa }}</span><br></span><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.proprietarioAnterior.pessoas\"><span ng-show=\"itemOriginal.nomeProprietarioReceita !== null\"><label class=label-component>Nome:</label><span class=span-original-component>{{ itemOriginal.nomeProprietarioReceita }}</span><br></span><span ng-show=\"itemOriginal.numeroDocumento !== null\"><label class=label-component>CPF/CNPJ:</label><span class=span-original-component>{{ itemOriginal.numeroDocumento }}</span><br></span></span></td><td md-cell><div id=proprietarioAnterior><label class=label-titulo>Proprietário Anterior</label><br><span ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal !== null\"><label class=label-component>Tipo Proprietário:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade !== null\"><label class=label-component>Órgão/Entidade:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade, homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.codigoUg !== null\"><label class=label-component>UG Específica:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.codigoUg, homCtrl.imovelAlterado.proprietarioAnterior.codigoUg) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.codigoUg }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj !== null\"><label class=label-component>Não possui CPF/CNPJ:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj, homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.justificativa !== null\"><label class=label-component>Justificativa:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.justificativa, homCtrl.imovelAlterado.proprietarioAnterior.justificativa) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.justificativa }}</span><br></span><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.proprietarioAnterior.pessoas\"><span ng-show=\"itemAlterado.nomeProprietarioReceita !== null\"><label class=label-component>Nome:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.pessoas[$index].nomeProprietarioReceita, homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.nomeProprietarioReceita }}</span><br></span><span ng-show=\"itemAlterado.numeroDocumento !== null\"><label class=label-component>CPF/CNPJ:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.pessoas[$index].numeroDocumento, homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.numeroDocumento }}</span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar dados de Incorporação\" name=proprietarioAnterior ng-model=homCtrl.informacoesRecusada.proprietarioAnterior ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.PROPRIETARIO_ANTERIOR)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.motivoAlteracao !== null\"><td md-cell colspan=\"{{ homCtrl.sizeColspanFull() }}\"><label class=label-titulo>Motivo da Alteração</label><br><label class=label-component>Motivo:</label><span class=span-original-component>{{ homCtrl.imovelAlterado.motivoAlteracao }}</span></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-dados-gerais-imovel/dados-do-proprietario.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados do Proprietário/Titular</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao != null\"><td md-cell>Tipo de Proprietário</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.id, homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de proprietário\" name=tipoProprietarioReal ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.tipoProprietarioReal.id, homCtrl.imovelAlterado.proprietario.tipoProprietarioReal.id)\" ng-checked=\"homCtrl.exists(\'tipoProprietarioReal\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietario.isIntegralmente != null\"><td md-cell>Tipo (Integral/Parcial)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietario.isIntegralmente ? \"Integral\" : \"Parcial\" }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.isIntegralmente, homCtrl.imovelAlterado.proprietario.isIntegralmente) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietario.isIntegralmente ? \"Integral\" : \"Parcial\" }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo (integral/parcial)\" name=tipoIntegralParcialProprietario ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.isIntegralmente, homCtrl.imovelAlterado.proprietario.isIntegralmente)\" ng-checked=\"homCtrl.exists(\'tipoIntegralParcialProprietario\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietario.tipoProprietarioParcial.descricao != null\"><td md-cell>Proprietário Parcial</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietario.tipoProprietarioParcial.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.tipoProprietarioParcial.id, homCtrl.imovelAlterado.proprietario.tipoProprietarioParcial.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietario.tipoProprietarioParcial.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de proprietário parcial\" name=tipoProprietarioParcial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.tipoProprietarioParcial.id, homCtrl.imovelAlterado.proprietario.tipoProprietarioParcial.id)\" ng-checked=\"homCtrl.exists(\'tipoProprietarioParcial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietario.orgaoEntidade != null\"><td md-cell>Órgão/Entidade</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietario.orgaoEntidade }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.orgaoEntidade, homCtrl.imovelAlterado.proprietario.orgaoEntidade) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietario.orgaoEntidade }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar órgão/entidade\" name=orgaoEntidadeProprietario ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.orgaoEntidade, homCtrl.imovelAlterado.proprietario.orgaoEntidade)\" ng-checked=\"homCtrl.exists(\'orgaoEntidadeProprietario\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietario.codigoUg != null\"><td md-cell>UG Específica</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietario.codigoUg }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.codigoUg, homCtrl.imovelAlterado.proprietario.codigoUg) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietario.codigoUg }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar UG específica\" name=ugEspecificaProprietario ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.codigoUg, homCtrl.imovelAlterado.proprietario.codigoUg)\" ng-checked=\"homCtrl.exists(\'ugEspecificaProprietario\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"item.nomeProprietarioReceita != null\"><td md-cell>CPF/CNPJ - Nome</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"item in homCtrl.imovelOriginal.proprietario.pessoas\">{{ item.numeroDocumento | brCpfCnpj }} - {{ item.nomeProprietarioReceita }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietario.pessoas, homCtrl.imovelAlterado.proprietario.pessoas) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in homCtrl.imovelAlterado.proprietario.pessoas\">{{ item.numeroDocumento | brCpfCnpj }} - {{ item.nomeProprietarioReceita }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar terceiros\" name=terceiros ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietario.pessoas, homCtrl.imovelAlterado.proprietario.pessoas)\" ng-checked=\"homCtrl.exists(\'terceiros\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Órgão ou entidade responsável pela gestão do imóvel</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioTitular.tipoProprietarioReal.descricao != null\"><td md-cell>Tipo de Titular</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioTitular.tipoProprietarioReal.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioTitular.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietarioTitular.tipoProprietarioReal.descricao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioTitular.tipoProprietarioReal.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de titular\" name=tipoTitular ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioTitular.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietarioTitular.tipoProprietarioReal.descricao)\" ng-checked=\"homCtrl.exists(\'tipoTitular\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioTitular.orgaoEntidade != null\"><td md-cell>Órgão/Entidade</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioTitular.orgaoEntidade }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioTitular.orgaoEntidade, homCtrl.imovelAlterado.proprietarioTitular.orgaoEntidade) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioTitular.orgaoEntidade }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar órgão/entidade do titular\" name=orgaoEntidadeTitular ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioTitular.orgaoEntidade, homCtrl.imovelAlterado.proprietarioTitular.orgaoEntidade)\" ng-checked=\"homCtrl.exists(\'orgaoEntidadeTitular\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioTitular.codigoUg != null\"><td md-cell>UG Específica</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioTitular.codigoUg }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioTitular.codigoUg, homCtrl.imovelAlterado.proprietarioTitular.codigoUg) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioTitular.codigoUg }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar UG específica do titular\" name=ugEspecificaTitular ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioTitular.codigoUg, homCtrl.imovelAlterado.proprietarioTitular.codigoUg)\" ng-checked=\"homCtrl.exists(\'ugEspecificaTitular\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioTitular.cnpjAdministracaoIndireta != null\"><td md-cell>CNPJ do Órgão</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioTitular.cnpjAdministracaoIndireta }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioTitular.cnpjAdministracaoIndireta, homCtrl.imovelAlterado.proprietarioTitular.cnpjAdministracaoIndireta) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioTitular.cnpjAdministracaoIndireta }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar CNPJ do órgão\" name=cnpjOrgaoTitular ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioTitular.cnpjAdministracaoIndireta, homCtrl.imovelAlterado.proprietarioTitular.cnpjAdministracaoIndireta)\" ng-checked=\"homCtrl.exists(\'cnpjOrgaoTitular\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Proprietário Anterior</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao != null\"><td md-cell>Tipo de Proprietário</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de proprietario anterior\" name=tipoProprietarioAnterior ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.tipoProprietarioReal.descricao, homCtrl.imovelAlterado.proprietarioAnterior.tipoProprietarioReal.descricao)\" ng-checked=\"homCtrl.exists(\'tipoProprietarioAnterior\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade != null\"><td md-cell>Órgão/Entidade</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade, homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar órgão/entidade do proprietário anterior\" name=orgaoEntidadeAnterior ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.orgaoEntidade, homCtrl.imovelAlterado.proprietarioAnterior.orgaoEntidade)\" ng-checked=\"homCtrl.exists(\'orgaoEntidadeAnterior\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.codigoUg != null\"><td md-cell>UG Específica</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioAnterior.codigoUg }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.codigoUg, homCtrl.imovelAlterado.proprietarioAnterior.codigoUg) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.codigoUg }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar UG específica do proprietário anterior\" name=ugEspecificaAnterior ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.codigoUg, homCtrl.imovelAlterado.proprietarioAnterior.codigoUg)\" ng-checked=\"homCtrl.exists(\'ugEspecificaAnterior\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj != null\"><td md-cell>Não possui CPF/CNPJ</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj, homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar posse CPF/CNPJ\" name=naoPossuiCpfOuCnpj ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.naoPossuiCpfOuCnpj, homCtrl.imovelAlterado.proprietarioAnterior.naoPossuiCpfOuCnpj)\" ng-checked=\"homCtrl.exists(\'naoPossuiCpfOuCnpj\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.proprietarioAnterior.justificativa != null\"><td md-cell>Justificativa</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.proprietarioAnterior.justificativa }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.justificativa, homCtrl.imovelAlterado.proprietarioAnterior.justificativa) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.proprietarioAnterior.justificativa }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar justificativa\" name=justificativaTerceiros ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.justificativa, homCtrl.imovelAlterado.proprietarioAnterior.justificativa)\" ng-checked=\"homCtrl.exists(\'justificativaTerceiros\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=homCtrl.imovelAlterado.proprietarioAnterior.pessoas><td md-cell>CPF/CNPJ - Nome</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"item in homCtrl.imovelOriginal.proprietarioAnterior.pessoas\">{{ item.numeroDocumento | brCpfCnpj }} - {{ item.nomeProprietarioReceita }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.proprietarioAnterior.pessoas, homCtrl.imovelAlterado.proprietarioAnterior.pessoas) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in homCtrl.imovelAlterado.proprietarioAnterior.pessoas\">{{ item.numeroDocumento | brCpfCnpj }} - {{ item.nomeProprietarioReceita }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar terceiros anterior\" name=terceirosAnterior ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.proprietarioAnterior.pessoas, homCtrl.imovelAlterado.proprietarioAnterior.pessoas)\" ng-checked=\"homCtrl.exists(\'terceirosAnterior\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-dados-gerais-imovel/detalhar-imovel-dados-gerais-novo.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><div style=\"margin-top: 10px; margin-bottom: 25px\"><label class=label-titulo>Detalhes do Imóvel</label><br><span ng-show=\"homCtrl.imovelOriginal.naturezaImovel !== null\"><label class=label-component>Natureza do imóvel:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.naturezaImovel.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.numeroInscricaoMunicipal !== null || homCtrl.imovelOriginal.numeroInscricaoRural !== null\"><label class=label-component>Nº de Inscrição municipal/rural:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.numeroInscricaoMunicipal ? homCtrl.imovelOriginal.numeroInscricaoMunicipal : homCtrl.imovelOriginal.numeroInscricaoRural}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.observacoesGerais !== null\"><label class=label-component>Observações Gerais:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.observacoesGerais }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.imovelOriundo !== null\"><label class=label-component>Imóvel oriundo de um sistema legado do Patrimônio:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.imovelOriundo ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.sistema !== null\"><span ng-repeat=\"sis in homCtrl.imovelOriginal.sistema\"><label class=label-component>Sistema:</label>{{ sis.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.dsSiapa !== null\"><label class=label-component>RIP SIAPA:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.dsSiapa}} </span></span><span ng-show=\"homCtrl.imovelOriginal.dsSpiunet !== null\"><label class=label-component>RIP SPIUNet:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.dsSpiunet}} </span></span><span ng-show=\"homCtrl.imovelOriginal.dsNbp !== null\"><label class=label-component>NBP:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.dsNbp}}</span></span></div></td><td md-cell><div style=\"margin-top: 10px; margin-bottom: 25px\"><div id=detalhesImovel><label class=label-titulo>Detalhes do Imóvel</label><br><span ng-show=\"homCtrl.imovelAlterado.naturezaImovel !== null\"><label class=label-component>Natureza do imóvel:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.naturezaImovel.descricao, homCtrl.imovelAlterado.naturezaImovel.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.naturezaImovel.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.numeroInscricaoMunicipal !== null || homCtrl.imovelAlterado.numeroInscricaoRural !== null\"><label class=label-component>Nº de Inscrição municipal/rural:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroInscricaoMunicipal, homCtrl.imovelAlterado.numeroInscricaoMunicipal) ? \'span-alterado-component\' : \'span-original-component\'\" ng-show=homCtrl.imovelAlterado.numeroInscricaoMunicipal>{{ homCtrl.imovelAlterado.numeroInscricaoMunicipal }} </span><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroInscricaoRural, homCtrl.imovelAlterado.numeroInscricaoRural) ? \'span-alterado-component\' : \'span-original-component\'\" ng-show=homCtrl.imovelAlterado.numeroInscricaoRural>{{ homCtrl.imovelAlterado.numeroInscricaoRural }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.observacoesGerais !== null\"><label class=label-component>Observações Gerais:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.observacoesGerais, homCtrl.imovelAlterado.observacoesGerais) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.observacoesGerais}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.imovelOriundo !== null\"><label class=label-component>Imóvel oriundo de um sistema legado do Patrimônio:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.imovelOriundo, homCtrl.imovelAlterado.imovelOriundo) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.imovelOriundo ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.sistema !== null\"><!-- <label class=\"label-component\">Sistema:</label>\n                  <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.sistema.descricao,\n                                    homCtrl.imovelAlterado.sistema.descricao) ? \'span-alterado-component\' :\n                                    \'span-original-component\'\">\n                    {{ homCtrl.imovelAlterado.sistema.descricao}}\n                  </span> --> <span ng-repeat=\"sis in homCtrl.imovelAlterado.sistema\"><label class=label-component>Sistema:</label>{{ sis.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.dsSiapa !== null\"><label class=label-component>RIP SIAPA:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.dsSiapa, homCtrl.imovelAlterado.dsSiapa) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.dsSiapa }} </span></span><span ng-show=\"homCtrl.imovelAlterado.dsSpiunet !== null\"><label class=label-component>RIP SPIUNet:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.dsSpiunet, homCtrl.imovelAlterado.dsSpiunet) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.dsSpiunet}} </span></span><span ng-show=\"homCtrl.imovelAlterado.dsNbp !== null\"><label class=label-component>NBP:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.dsNbp, homCtrl.imovelAlterado.dsNbp) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.dsNbp}}</span></span></div></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar detalhes do Imóvel\" name=detalhesImovel ng-model=homCtrl.informacoesRecusada.detalhesImovel ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.DETALHES_IMOVEL)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><div style=\"margin-top: 10px; margin-bottom: 25px\"><label class=label-titulo>Dados de Incorporação</label><br><span ng-show=\"homCtrl.imovelOriginal.numeroProcesso !== null\"><label class=label-component>Nº do Processo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.numeroProcesso }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.tipoIncorporacao !== null\"><label class=label-component>Forma de Incorporação:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.tipoIncorporacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.tipoAquisicao !== null\"><label class=label-component>Tipo de Aquisição:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.tipoAquisicao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.tipoFracionamento !== null\"><label class=label-component>Tipo de Fracionamento/Parcelamento:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.tipoFracionamento.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal !== null\"><label class=label-component>Tipo de Contrato Pessoal:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.valorEncargo !== null\"><label class=label-component>Valor do Encargo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.valorEncargo }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.outroContatoPessoal !== null\"><label class=label-component>Outro Contrato Pessoal:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.outroContatoPessoal }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.isExOficio !== null\"><label class=label-component>Ex-Officio:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.isExOficio ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=false><label class=label-component>A Pedido:</label><span class=span-original-component>???</span><br></span><span ng-show=false><label class=label-component>N do Processo LPB/LMEO/Identificação de área da União:</label><span class=span-original-component>???</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.codigoOrigem !== null\"><label class=label-component>RIP de Origem:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.codigoOrigem }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.ripsFusao.length !== 0\"><label class=label-component>RIP do Imóvel Remembrado:</label><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.ripsFusao\" class=span-original-component>{{ itemOriginal.ripsFusao.rip }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.fundamento.length !== 0\"><label class=label-component>Fundamento:</label><span ng-repeat=\"item in homCtrl.imovelOriginal.incorporacao.fundamento\" class=span-original-component>{{ item.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao !== null\"><label class=label-component>Situação da Incorporação:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.incorporacao.direitoIncorporado !== null\"><label class=label-component>Direito Adquirido/Incorporado:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.incorporacao.direitoIncorporado.descricao }}</span></span></div></td><td md-cell><div style=\"margin-top: 10px; margin-bottom: 25px\"><div id=dadosIncorporacao><label class=label-titulo>Dados de Incorporação</label><br><span ng-show=\"homCtrl.imovelAlterado.numeroProcesso !== null\"><label class=label-component>Nº do Processo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroProcesso, homCtrl.imovelAlterado.numeroProcesso) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.numeroProcesso }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoIncorporacao !== null\"><label class=label-component>Forma de Incorporação:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoIncorporacao.descricao, homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.descricao ) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoAquisicao !== null\"><label class=label-component>Tipo de Aquisição:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoAquisicao.descricao, homCtrl.imovelAlterado.incorporacao.tipoAquisicao.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoAquisicao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.tipoFracionamento !== null\"><label class=label-component>Tipo de Fracionamento/Parcelamento:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.tipoFracionamento.descricao, homCtrl.imovelAlterado.tipoFracionamento.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.tipoFracionamento.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal !== null\"><label class=label-component>Tipo de Contrato Pessoal:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal.descricao, homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.valorEncargo !== null\"><label class=label-component>Valor do Encargo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.valorEncargo, homCtrl.imovelAlterado.incorporacao.valorEncargo) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.valorEncargo }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.outroContatoPessoal !== null\"><label class=label-component>Outro Contrato Pessoal:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.outroContatoPessoal, homCtrl.imovelAlterado.outroContatoPessoal) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.outroContatoPessoal }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.isExOficio !== null\"><label class=label-component>Ex-Officio:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.isExOficio, homCtrl.imovelAlterado.incorporacao.isExOficio) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.isExOficio ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=false><label class=label-component>A Pedido:</label><span ng-class=\"homCtrl.isDadoDivergente() ? \'span-alterado-component\' : \'span-original-component\'\">???</span><br></span><span ng-show=false><label class=label-component>N do Processo LPB/LMEO/Identificação de área da União:</label><span ng-class=\"homCtrl.isDadoDivergente() ? \'span-alterado-component\' : \'span-original-component\'\">???</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.codigoOrigem !== null\"><label class=label-component>RIP de Origem:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.codigoOrigem, homCtrl.imovelAlterado.incorporacao.codigoOrigem) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.codigoOrigem }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.ripsFusao.length !== 0\"><label class=label-component>RIP do Imóvel Remembrado:</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.ripsFusao\" ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.ripsFusao[$index].ripsFusao.rip, itemAlterado.ripsFusao.rip) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.ripsFusao.rip }} </span><span class=span-alterado-component ng-show=\"homCtrl.imovelOriginal.ripsFusao.length > homCtrl.imovelAlterado.ripsFusao.length\">Informação Apagada</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.fundamento.length !== 0\"><label class=label-component>Fundamento:</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.incorporacao.fundamento\" ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.fundamento[$index].descricao, itemAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.descricao }} </span><span class=span-alterado-component ng-show=\"homCtrl.imovelOriginal.incorporacao.fundamento.length > homCtrl.imovelAlterado.incorporacao.fundamento.length\">Informação Apagada</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao !== null\"><label class=label-component>Situação da Incorporação:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao.descricao, homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.incorporacao.direitoIncorporado !== null\"><label class=label-component>Direito Adquirido/Incorporado:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.direitoIncorporado.descricao, homCtrl.imovelAlterado.incorporacao.direitoIncorporado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.incorporacao.direitoIncorporado.descricao }}</span></span></div></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar dados de Incorporação\" name=dadosIncorporacao ng-model=homCtrl.informacoesRecusada.dadosIncorporacao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.DADOS_INCORPORACAO)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.titulos.length !== 0 || homCtrl.imovelAlterado.titulos.length !== 0\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><br><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.titulos\"><span ng-show=itemOriginal.nome><label class=label-titulo>Título Aquisitivo - {{ itemOriginal.nome }}</label><br></span><span ng-show=\"itemOriginal.tipoTituloAquisitivo !== null\"><label class=label-component>Tipo:</label><span class=span-original-component>{{ itemOriginal.tipoTituloAquisitivo.descricao }}</span><br></span><span ng-show=\"itemOriginal.tipoAtoLegal !== null\"><label class=label-component>Tipo de Ato Legal:</label><span class=span-original-component>{{ itemOriginal.tipoAtoLegal.descricao }}</span><br></span><span ng-show=\"itemOriginal.numero !== null\"><label class=label-component>Número:</label><span class=span-original-component>{{ itemOriginal.numero }}</span><br></span><span ng-show=\"itemOriginal.nomeArquivo !== null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.url }}\" download=\"{{ itemOriginal.nomeArquivo }}\">{{ itemOriginal.nomeArquivo }}</a></span><br></span><span ng-show=\"itemOriginal.dataAssinatura !== null\"><label class=label-component>Data da Assinatura:</label><span class=span-original-component>{{ itemOriginal.dataAssinatura | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.dataFinalVigencia !== null\"><label class=label-component>Data de final da Vigência:</label><span class=span-original-component>{{ itemOriginal.dataFinalVigencia | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.publicacao !== null\"><label class=label-component>Página:</label><span class=span-original-component>{{ itemOriginal.publicacao.pagina }}</span><br></span><span ng-show=\"itemOriginal.publicacao !== null\"><label class=label-component>Seção</label><span class=span-original-component>{{ itemOriginal.publicacao.secao }}</span><br></span><span ng-show=\"itemOriginal.publicacao !== null\"><label class=label-component>Data:</label><span class=span-original-component>{{ itemOriginal.publicacao.dataPublicacao | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.moeda !== null\"><label class=label-component>Moeda:</label><span class=span-original-component>{{ itemOriginal.moeda.descricao }}</span><br></span><span ng-show=\"itemOriginal.valorContrato !== null\"><label class=label-component>Valor do Contrato:</label><span class=span-original-component>{{ itemOriginal.valorContrato }}</span><br></span><span ng-show=\"itemOriginal.observacao !== null\"><label class=label-component>Observações/Anotações:</label><span class=span-original-component>{{ itemOriginal.observacao }}</span><br></span><span ng-show=\"itemOriginal.encargos.length !== 0\"><span ng-repeat=\"encargoOriginal in  itemOriginal.encargos\"><span ng-show=\"encargoOriginal.descricao !== null\"><label class=label-component>Encargo:</label><span class=span-original-component>{{ encargoOriginal.descricao }}</span><br></span><span ng-show=\"encargoOriginal.prazoCumprimento !== null\"><label class=label-sub-component>Prazo para cumprimento:</label><span class=span-original-component>{{ encargoOriginal.prazoCumprimento | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"encargoOriginal.cumprimento !== null\"><label class=label-sub-component>Cumprimento do Encargo:</label><span class=span-original-component>{{ encargoOriginal.cumprimento }}</span><br></span></span></span><br><br></span></td><td md-cell><div id=tituloAquisitivo><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.titulos\"><span ng-show=\"itemAlterado.nome !== null\"><label class=label-titulo>Título Aquisitivo - {{ itemAlterado.nome }}</label><br></span><span ng-show=\"itemAlterado.tipoTituloAquisitivo !== null\"><label class=label-component>Tipo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].tipoTituloAquisitivo.descricao, itemAlterado.tipoTituloAquisitivo.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.tipoTituloAquisitivo.descricao }}</span><br></span><span ng-show=\"itemAlterado.tipoAtoLegal !== null\"><label class=label-component>Tipo de Ato Legal:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].tipoAtoLegal.descricao, itemAlterado.tipoAtoLegal.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.tipoAtoLegal.descricao }}</span><br></span><span ng-show=\"itemAlterado.numero !== null\"><label class=label-component>Número:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].numero, itemAlterado.numero) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.numero }}</span><br></span><span ng-show=\"itemAlterado.nomeArquivo !== null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].nomeArquivo, itemAlterado.nomeArquivo) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.url }}\" download=\"{{ itemAlterado.nomeArquivo }}\">{{ itemAlterado.nomeArquivo }}</a></span><br></span><span ng-show=\"itemAlterado.dataAssinatura !== null\"><label class=label-component>Data da Assinatura:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].tipoTituloAquisitivo.dataAssinatura, itemAlterado.tipoTituloAquisitivo.dataAssinatura) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataAssinatura | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.dataFinalVigencia !== null\"><label class=label-component>Data de final da Vigência:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].tipoTituloAquisitivo.dataFinalVigencia , itemAlterado.tipoTituloAquisitivo.dataFinalVigencia) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataFinalVigencia | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.publicacao !== null\"><label class=label-component>Página:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].publicacao.pagina , itemAlterado.publicacao.pagina) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.publicacao.pagina }}</span><br></span><span ng-show=\"itemAlterado.publicacao !== null\"><label class=label-component>Seção</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].publicacao.secao, itemAlterado.publicacao.secao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.publicacao.secao }}</span><br></span><span ng-show=\"itemAlterado.publicacao !== null\"><label class=label-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].publicacao.dataPublicacao, itemAlterado.publicacao.dataPublicacao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.publicacao.dataPublicacao | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.moeda !== null\"><label class=label-component>Moeda:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].moeda.descricao, itemAlterado.moeda.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.moeda.descricao }}</span><br></span><span ng-show=\"itemAlterado.valorContrato !== null\"><label class=label-component>Valor do Contrato:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].valorContrato, itemAlterado.valorContrato) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.valorContrato }}</span><br></span><span ng-show=\"itemAlterado.observacao !== null\"><label class=label-component>Observações/Anotações:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$index].observacao, itemAlterado.observacao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.observacao }}</span><br></span><span ng-show=\"itemAlterado.encargos.length !== 0\"><span ng-repeat=\"encargoAlterado in  itemAlterado.encargos\"><span ng-show=\"encargoAlterado.descricao !== null\"><label class=label-component>Encargo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$parent.$index].encargos[$index].descricao, encargoAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ encargoAlterado.descricao }}</span><br></span><span ng-show=\"encargoAlterado.prazoCumprimento !== null\"><label class=label-sub-component>Prazo para cumprimento:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$parent.$index].encargos[$index].prazoCumprimento , encargoAlterado.prazoCumprimento ) ? \'span-alterado-component\' : \'span-original-component\'\">{{ encargoAlterado.prazoCumprimento | date:\"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"encargoAlterado.cumprimento !== null\"><label class=label-sub-component>Cumprimento do Encargo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.titulos[$parent.$index].encargos[$index].cumprimento, encargoAlterado.cumprimento) ? \'span-alterado-component\' : \'span-original-component\'\">{{ encargoAlterado.cumprimento }}</span><br></span></span></span><br><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Titulos Aquisitivos\" name=tituloAquisitivo ng-model=homCtrl.informacoesRecusada.tituloAquisitivo ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.MATRICULA_TRANSCRICAO)></md-checkbox><md-checkbox aria-label=\"Recusar Matricula/Transcrição\" name=matriculaTranscricao ng-model=homCtrl.informacoesRecusada.matriculaTranscricao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.MATRICULA_TRANSCRICAO)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.incorporacao.matriculas.length !== 0 || homCtrl.imovelAlterado.incorporacao.matriculas.length !== 0\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><br><span ng-show=\"homCtrl.imovelOriginal.incorporacao.matriculas.length !== 0\"><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.incorporacao.matriculas\"><span ng-show=\"itemOriginal.numero !== null\"><label class=label-titulo>Matrícula/Transcrição - {{ itemOriginal.numero }}</label><br></span><span ng-show=\"itemOriginal.uf !== null\"><label class=label-component>Município/UF:</label><span class=span-original-component>{{ itemOriginal.municipio }}/{{ itemOriginal.uf }}</span><br></span><span ng-show=\"itemOriginal.cartorioID !== null\"><label class=label-component>Nome do Cartório:</label><span class=span-original-component>{{ itemOriginal.cartorio.nome }}</span><br></span><span ng-show=\"itemOriginal.dataMatricula !== null\"><label class=label-component>Data da Matrícula:</label><span class=span-original-component>{{ itemOriginal.dataMatricula | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.dataRegistro !== null\"><label class=label-component>Data do Registro:</label><span class=span-original-component>{{ itemOriginal.dataRegistro | date: \"dd/MM/yyyy\" }}</span><br></span><br></span></span></td><td md-cell><div id=matriculaTranscricao><span ng-show=\"homCtrl.imovelAlterado.incorporacao.matriculas.length !== 0\"><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.incorporacao.matriculas\"><span ng-show=\"itemAlterado.numero !== null\"><label class=label-titulo>Matrícula/Transcrição - {{ itemAlterado.numero }}</label><br></span><span ng-show=\"itemAlterado.uf !== null\"><label class=label-component>Município/UF:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.matriculas[$index].uf , itemAlterado.uf) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.municipio }}/{{ itemAlterado.uf }}</span><br></span><span ng-show=\"itemAlterado.cartorioID !== null\"><label class=label-component>Nome do Cartório:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.matriculas[$index].cartorioID , itemAlterado.cartorioID) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.cartorio.nome }}</span><br></span><span ng-show=\"itemAlterado.dataMatricula !== null\"><label class=label-component>Data da Matrícula:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.matriculas[$index].dataMatricula , itemAlterado.dataMatricula) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataMatricula | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.dataRegistro !== null\"><label class=label-component>Data do Registro:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.matriculas[$index].dataRegistro , itemAlterado.dataRegistro) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataRegistro | date: \"dd/MM/yyyy\" }}</span><br></span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Matricula/Transcrição\" name=matriculaTranscricao ng-model=homCtrl.informacoesRecusada.matriculaTranscricao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.MATRICULA_TRANSCRICAO)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.incorporacao.certidoesCartoriais.length !== 0 || homCtrl.imovelAlterado.incorporacao.certidoesCartoriais.length !== 0\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Documentos Cartoriais</label><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.incorporacao.certidoesCartoriais \"><br><span ng-show=\"itemOriginal.descricao !== null\"><label class=label-component>Descrição:</label><span class=span-original-component>{{ itemOriginal.descricao }}</span><br></span><span ng-show=\"itemOriginal.data !== null\"><label class=label-component>Data:</label><span class=span-original-component>{{ itemOriginal.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.cartorio !== null\"><label class=label-component>Cartório:</label><span class=span-original-component>{{ itemOriginal.cartorioRecuperado.nome }}</span><br></span><span ng-show=\"itemOriginal.arquivo.nomeOriginal !== null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.arquivo.url }}\" download=\"{{ itemOriginal.arquivo.nomeOriginal }}\">{{ itemOriginal.arquivo.nomeOriginal }}</a></span><br></span></span></td><td md-cell><div id=documentoCartorial><label class=label-titulo>Documentos Cartoriais</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.incorporacao.certidoesCartoriais\"><br><span ng-show=\"itemAlterado.descricao !== null\"><label class=label-component>Descrição:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais[$index].descricao , itemAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.descricao }}</span><br></span><span ng-show=\"itemAlterado.data !== null\"><label class=label-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais[$index].data , itemAlterado.data) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.cartorio !== null\"><label class=label-component>Cartório:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais[$index].cartorio , itemAlterado.cartorio) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.cartorioRecuperado.nome }}</span><br></span><span ng-show=\"itemAlterado.arquivo.nomeOriginal !== null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais[$index].arquivo.nomeOriginal , itemAlterado.arquivo.nomeOriginal) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.arquivo.url }}\" download=\"{{ itemAlterado.arquivo.nomeOriginal }}\">{{ itemAlterado.arquivo.nomeOriginal }}</a></span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Documentos Cartoriais\" name=documentoCartorial ng-model=homCtrl.informacoesRecusada.documentoCartorial ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.DOCUMENTO_CARTORIAL)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><div style=\"margin-top: 5px\"><label class=label-titulo>Fotos do Imóvel</label><br><span><inc-modal-fotos titulo-modal=\"Fotos do Imóvel\" fotos=homCtrl.imovelOriginal.imagens></inc-modal-fotos></span></div></td><td md-cell><div style=\"margin-top: 5px\"><div id=fotosImovelGeral><label class=label-titulo style=\"margin-top: 5px\">Fotos do Imóvel</label><br><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.imagens , homCtrl.imovelAlterado.imagens) ? \'span-alterado-component\' : \'span-original-component\'\"><inc-modal-fotos titulo-modal=\"Fotos do Imóvel\" fotos=homCtrl.imovelAlterado.imagens></inc-modal-fotos><span ng-hide=true ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.imagens , homCtrl.imovelAlterado.imagens) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.imagens }}</span></span></div></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Fotos do Imóvel\" name=fotosImovelGeral ng-model=homCtrl.informacoesRecusada.fotosImovelGeral ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.FOTOS_IMOVEL_GERAL)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-dados-gerais-imovel/detalhar-imovel-dados-gerais.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Detalhes do Imóvel</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.naturezaImovel.descricao != null\"><td md-cell>Natureza do Imóvel</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.naturezaImovel.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.naturezaImovel.id, homCtrl.imovelAlterado.naturezaImovel.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.naturezaImovel.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar natureza do imóvel\" name=naturezaImovel ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.naturezaImovel.id, homCtrl.imovelAlterado.naturezaImovel.id)\" ng-checked=\"homCtrl.exists(\'naturezaImovel\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.numeroInscricaoMunicipal != null\"><td md-cell>Nº da Inscrição Municipal</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.numeroInscricaoMunicipal }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroInscricaoMunicipal, homCtrl.imovelAlterado.numeroInscricaoMunicipal) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.numeroInscricaoMunicipal }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número da inscrição municipal\" name=numeroInscricaoMunicipal ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.numeroInscricaoMunicipal, homCtrl.imovelAlterado.numeroInscricaoMunicipal)\" ng-checked=\"homCtrl.exists(\'numeroInscricaoMunicipal\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.numeroInscricaoRural != null\"><td md-cell>Nº da Inscrição Rural</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.numeroInscricaoRural }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroInscricaoRural, homCtrl.imovelAlterado.numeroInscricaoRural) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.numeroInscricaoRural }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número da inscrição rural\" name=numeroInscricaoRural ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.numeroInscricaoRural, homCtrl.imovelAlterado.numeroInscricaoRural)\" ng-checked=\"homCtrl.exists(\'numeroInscricaoRural\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.observacoesGerais != null\"><td md-cell>Observações Gerais</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.observacoesGerais }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.observacoesGerais, homCtrl.imovelAlterado.observacoesGerais) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.observacoesGerais }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar observações gerais\" name=observacoesGeraisImovel ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.observacoesGerais, homCtrl.imovelAlterado.observacoesGerais)\" ng-checked=\"homCtrl.exists(\'observacoesGeraisImovel\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados de Incorporação</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.numeroProcesso != null\"><td md-cell>Nº do Processo</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.numeroProcesso }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.numeroProcesso, homCtrl.imovelAlterado.numeroProcesso) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.numeroProcesso }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número do processo\" name=numeroProcesso ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.numeroProcesso, homCtrl.imovelAlterado.numeroProcesso)\" ng-checked=\"homCtrl.exists(\'numeroProcesso\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.descricao != null\"><td md-cell>Forma de Incorporação</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.tipoIncorporacao.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoIncorporacao.id, homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar forma de incorporação\" name=formaIncorporacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.tipoIncorporacao.id, homCtrl.imovelAlterado.incorporacao.tipoIncorporacao.id)\" ng-checked=\"homCtrl.exists(\'formaIncorporacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoAquisicao.descricao != null\"><td md-cell>Tipo de Aquisição</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.tipoAquisicao.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoAquisicao.id, homCtrl.imovelAlterado.incorporacao.tipoAquisicao.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoAquisicao.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de aquisição\" name=tipoAquisicao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.tipoAquisicao.id, homCtrl.imovelAlterado.incorporacao.tipoAquisicao.id)\" ng-checked=\"homCtrl.exists(\'tipoAquisicao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.tipoFracionamento.descricao != null\"><td md-cell>Tipo de Fracionamento/Parcelamento</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.tipoFracionamento.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.tipoFracionamento.id, homCtrl.imovelAlterado.tipoFracionamento.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.tipoFracionamento.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de fracionamento/parcelamento\" name=tipoFracionamento ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.tipoFracionamento.id, homCtrl.imovelAlterado.tipoFracionamento.id)\" ng-checked=\"homCtrl.exists(\'tipoFracionamento\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.descricao != null\"><td md-cell>Tipo de Contrato Pessoal</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal.id, homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de contrato pessoal\" name=tipoContratoPessoal ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.tipoContratoPessoal.id, homCtrl.imovelAlterado.incorporacao.tipoContratoPessoal.id)\" ng-checked=\"homCtrl.exists(\'tipoContratoPessoal\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.outroContatoPessoal != null\"><td md-cell>Descrição do Contrato/Termo</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.outroContatoPessoal }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.outroContatoPessoal, homCtrl.imovelAlterado.incorporacao.outroContatoPessoal) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.outroContatoPessoal }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar descrição do contrato/termo\" name=outroContatoPessoal ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.outroContatoPessoal, homCtrl.imovelAlterado.incorporacao.outroContatoPessoal)\" ng-checked=\"homCtrl.exists(\'outroContatoPessoal\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell colspan=4><v-accordion><v-pane ng-repeat=\"item in homCtrl.titulosAquisitivos\"><v-pane-header>Título Aquisitivo - {{ item.tituloOriginal ? item.tituloOriginal.nome : item.tituloAlterado.nome }}</v-pane-header><v-pane-content><inc-homologar-dados-titulo-aquisitivo titulos=item index=$index fn-is-cadastro-alterado=homCtrl.isCadastroAlterado() fn-desabilitar-checkbox=\"homCtrl.desabilitarCheckbox(dadoOriginal, dadoAlterado)\" fn-is-dado-divergente=\"homCtrl.isDadoDivergente(dadoOriginal, dadoAlterado)\" exists=homCtrl.exists(campo) toggle=homCtrl.toggle(element)></inc-homologar-dados-titulo-aquisitivo></v-pane-content></v-pane></v-accordion></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.codigoOrigem != null\"><td md-cell>RIP de Origem</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.codigoOrigem }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.codigoOrigem, homCtrl.imovelAlterado.incorporacao.codigoOrigem) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.codigoOrigem }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar RIP de origem\" name=codigoOrigem ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.codigoOrigem, homCtrl.imovelAlterado.incorporacao.codigoOrigem)\" ng-checked=\"homCtrl.exists(\'codigoOrigem\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"itemAlterado.ripsFusao.rip != null\"><td md-cell>RIP do Imóvel Remembrado</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.ripsFusao\">{{ itemOriginal.ripsFusao.rip }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.ripsFusao, homCtrl.imovelAlterado.ripsFusao) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.ripsFusao\">{{ itemAlterado.ripsFusao.rip }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar RIPs do imóvel remembrado\" name=ripDoImovelRemembrado ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.ripsFusao, homCtrl.imovelAlterado.ripsFusao)\" ng-checked=\"homCtrl.exists(\'ripDoImovelRemembrado\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"item.descricao != null\"><td md-cell>Fundamento</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"item in homCtrl.imovelOriginal.incorporacao.fundamento\">{{ item.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.fundamento, homCtrl.imovelAlterado.incorporacao.fundamento) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in homCtrl.imovelAlterado.incorporacao.fundamento\">{{ item.descricao }}</span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fundamentos\" name=fundamentos ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.fundamento, homCtrl.imovelAlterado.incorporacao.fundamento)\" ng-checked=\"homCtrl.exists(\'fundamentos\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.descricao != null\"><td md-cell>Situação da Incorporação</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao.id, homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar situação da incorporação\" name=situacaoIncorporacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.situacaoIncorporacao.id, homCtrl.imovelAlterado.incorporacao.situacaoIncorporacao.id)\" ng-checked=\"homCtrl.exists(\'situacaoIncorporacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.incorporacao.direitoIncorporado.descricao != null\"><td md-cell>Direito Adquirido/Incorporado</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.incorporacao.direitoIncorporado.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.direitoIncorporado.id, homCtrl.imovelAlterado.incorporacao.direitoIncorporado.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.incorporacao.direitoIncorporado.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar direito adquirido/incorporado\" name=direitoIncorporado ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.direitoIncorporado.id, homCtrl.imovelAlterado.incorporacao.direitoIncorporado.id)\" ng-checked=\"homCtrl.exists(\'direitoIncorporado\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell colspan=4><v-accordion><v-pane ng-repeat=\"item in homCtrl.matriculas\"><v-pane-header>Matrícula/Transcrição - {{ item.matriculaOriginal ? item.matriculaOriginal.numero : item.matriculaAlterada.numero }}</v-pane-header><v-pane-content><inc-homologar-dados-matricula matriculas=item index=$index fn-is-cadastro-alterado=homCtrl.isCadastroAlterado() fn-desabilitar-checkbox=\"homCtrl.desabilitarCheckbox(dadoOriginal, dadoAlterado)\" fn-is-dado-divergente=\"homCtrl.isDadoDivergente(dadoOriginal, dadoAlterado)\" exists=homCtrl.exists(campo) toggle=homCtrl.toggle(element)></inc-homologar-dados-matricula></v-pane-content></v-pane></v-accordion></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.incorporacao.certidoesCartoriais.length > 0\"><td md-cell>Documentos Cartoriais</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"item in homCtrl.imovelOriginal.incorporacao.certidoesCartoriais\"><a ng-href=\"{{ item.arquivo.url }}\" download=\"{{ item.arquivo.nomeOriginal }}\">{{ item.arquivo.nomeOriginal }}</a></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais, homCtrl.imovelAlterado.incorporacao.certidoesCartoriais) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in homCtrl.imovelAlterado.incorporacao.certidoesCartoriais\"><a ng-href=\"{{ item.arquivo.url }}\" download=\"{{ item.arquivo.nomeOriginal }}\">{{ item.arquivo.nomeOriginal }}</a></span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar documentos cartoriais\" name=documentosCartoriais ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.incorporacao.certidoesCartoriais, homCtrl.imovelAlterado.incorporacao.certidoesCartoriais)\" ng-checked=\"homCtrl.exists(\'documentosCartoriais\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Fotos do Imóvel</th></tr><tr md-row><th md-column width=45% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=45%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><span><inc-modal-fotos titulo-modal=\"Fotos do Imóvel\" fotos=homCtrl.imovelOriginal.imagens></inc-modal-fotos></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.imagens, homCtrl.imovelAlterado.imagens) ? \'dado-alterado\' : \'\'\"><inc-modal-fotos titulo-modal=\"Fotos do Imóvel\" fotos=homCtrl.imovelAlterado.imagens></inc-modal-fotos></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fotos do imóvel\" name=fotosImovel ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.imagens, homCtrl.imovelAlterado.imagens)\" ng-checked=\"homCtrl.exists(\'fotosImovel\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-pendencia/dialog-detalhar-pendencia-imoveis.html","<md-dialog aria-label=\"Localização do imóvel\" flex=60 ng-cloak id=localizacaoMapa><form name=localizacaoImovel ng-submit=incluir()><md-toolbar><div class=md-toolbar-tools><h2>Localização do imóvel</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=mapaImovelConsultar><ci-mapa-incorporacao showminimapa=false tabindex=\"\" imovel=\"\" action=detalharImoveisPendencia habilitar-desenho=false></ci-mapa-incorporacao></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-unidade-autonoma/dados-da-edificacao.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Geometria da Edificação</th></tr><tr md-row><th md-column width=45%>Original</th><th md-column width=45%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell><inc-mapa-homologar ng-show=homCtrl.isCadastroAlterado() geometria=homCtrl.imovelOriginal.unidadeAutonoma.edificacao.geometria></inc-mapa-homologar></td><td md-cell><inc-mapa-homologar geometria=homCtrl.imovelAlterado.unidadeAutonoma.edificacao.geometria dado-alterado=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.geometria.coordinates, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.geometria.coordinates)\"></inc-mapa-homologar></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar geometria da edificação\" name=geometriaEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.geometria.coordinates, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.geometria.coordinates)\" ng-checked=\"homCtrl.exists(\'geometriaEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados da Edificação</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Tipo de Edificação</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao.id, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo da edificação\" name=tipoEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao.id, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao.id)\" ng-checked=\"homCtrl.exists(\'tipoEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Possui Elevador</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.boolToString(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador) }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador) ? \'dado-alterado\' : \'\'\">{{ homCtrl.boolToString(homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador) }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar possui elevador\" name=edificacaoPossuiElevador ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador)\" ng-checked=\"homCtrl.exists(\'edificacaoPossuiElevador\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Tipo de Projeto</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto.id, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de projeto\" name=tipoProjetoEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto.id, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto.id)\" ng-checked=\"homCtrl.exists(\'tipoProjetoEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Nome</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar nome da edificacao\" name=nomeEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome)\" ng-checked=\"homCtrl.exists(\'nomeEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Observações Gerais da Edificação</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar observações da edificação\" name=observacoesEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes)\" ng-checked=\"homCtrl.exists(\'observacoesEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Condições de Acessibilidade</th></tr><tr md-row><th md-column width=50%>Campo</th><th md-column width=20%>Original</th><th md-column width=20%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Há vagas próximas dos acessos de circulação de pedrestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (vagas)\" name=condicoesAcessibilidadeVagasEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga)\" ng-checked=\"homCtrl.exists(\'condicoesAcessibilidadeVagasEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (mobilidade)\" name=condicoesAcessibilidadeMobilidadeEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade)\" ng-checked=\"homCtrl.exists(\'condicoesAcessibilidadeMobilidadeEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (banheiro)\" name=condicoesAcessibilidadeBanheiroEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro)\" ng-checked=\"homCtrl.exists(\'condicoesAcessibilidadeBanheiroEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar condição de acessibilidade (elevador)\" name=condicoesAcessibilidadeElevadorEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador)\" ng-checked=\"homCtrl.exists(\'condicoesAcessibilidadeElevadorEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Documentação</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Documentos</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado() ng-repeat=\"item in homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao\"><a ng-href=\"{{ item.arquivo.url }}\" download=\"{{ item.arquivo.nomeOriginal }}\">{{ item.arquivo.nomeOriginal }}</a></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.documentacao) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"item in homCtrl.imovelAlterado.unidadeAutonoma.edificacao.documentacao\"><a ng-href=\"{{ item.arquivo.url }}\" download=\"{{ item.arquivo.nomeOriginal }}\">{{ item.arquivo.nomeOriginal }}</a></span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar documentação da edificação\" name=documentacaoEdificacao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.documentacao)\" ng-checked=\"homCtrl.exists(\'documentacaoEdificacao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-unidade-autonoma/detalhar-imovel-dados-edificacao-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao !== null\"><label class=label-component>Tipo de Edificação:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador !== null\"><label class=label-component>Possui Elevador:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto !== null\"><label class=label-component>Tipo de Projeto:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome !== null\"><label class=label-component>Nome da Edificação:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes !== null\"><label class=label-component>Observações Gerais:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes }}</span></span></td><td md-cell><div id=unidadeAutonomaEdificacao><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao !== null\"><label class=label-component>Tipo de Edificação:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoEdificacao.descricao, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoEdificacao.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador !== null\"><label class=label-component>Possui Elevador:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.possuiElevador, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.possuiElevador? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto !== null\"><label class=label-component>Tipo de Projeto:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.tipoProjeto.descricao, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.tipoProjeto.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome !== null\"><label class=label-component>Nome da Edificação:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.nome, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.nome }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes !== null\"><label class=label-component>Observações Gerais:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.edificacao.observacoes }}</span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Edificacao Unidade Autônoma\" name=unidadeAutonomaEdificacao ng-model=homCtrl.informacoesRecusada.unidadeAutonomaEdificacao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA_EDIFICACAO)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Condições de Acessibilidade</label><br><label class=label-component>Há vagas próximas dos acessos de circulação de pedestres devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade ? \"Sem Informação\" : homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga ? homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga : \"Sem Informação.\"}}</span><br><label class=label-component>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade ? \"Sem Informação\" : homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade ? homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade : \"Sem Informação.\"}}</span><br><label class=label-component>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade ? \"Sem Informação\" : homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro ? homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro : \"Sem Informação.\"}}</span><br><label class=label-component>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade ? \"Sem Informação\" : homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador ? homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador : \"Sem Informação.\"}}</span></td><td md-cell><div id=unidadeAutonomaAcessibilidade><label class=label-titulo>Condições de Acessibilidade</label><br><label class=label-component>Há vagas próximas dos acessos de circulação de pedestres devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.vaga, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade? \"Sem Informação\" : homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga ? homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.vaga : \"Sem Informação.\"}}</span><br><label class=label-component>Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.mobilidade, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade? \"Sem Informação\" : homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade ? homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.mobilidade : \"Sem Informação.\"}}</span><br><label class=label-component>Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.banheiro, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade? \"Sem Informação\" : homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro ? homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.banheiro : \"Sem Informação.\"}}</span><br><label class=label-component>Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.</label>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.condicoesAcessibilidade.elevador, homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.semInformacoesAcessibilidade? \"Sem Informação\" : homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador ? homCtrl.imovelAlterado.unidadeAutonoma.condicoesAcessibilidade.elevador : \"Sem Informação.\"}}</span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Acessibilidade Unidade Autônoma\" name=unidadeAutonomaAcessibilidade ng-model=homCtrl.informacoesRecusada.unidadeAutonomaAcessibilidade ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA_ACESSIBILIDADE)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Dados do Medidor</label><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.unidadeAutonoma.edificacao.dadosMedidor\"><br><span ng-show=\"itemOriginal.empresaFornecedora.tipoFornecedoraEnum !== null\"><label class=label-component>Tipo do Medidor:</label><span class=span-original-component>{{ itemOriginal.empresaFornecedora.tipoFornecedoraEnum.tipoFornecedora }}</span><br></span><span ng-show=\"itemOriginal.codigo !== null\"><label class=label-component>Código do medidor:</label><span class=span-original-component>{{ itemOriginal.codigo }}</span><br></span><span ng-show=\"itemOriginal.empresaFornecedora !== null\"><label class=label-component>Empresa Fornecedora:</label><span class=span-original-component>{{ itemOriginal.empresaFornecedora.nome }}</span><br></span></span></td><td md-cell><div id=unidadeAutonomaEdificacaoDadosMedidor><label class=label-titulo>Dados do Medidor</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.unidadeAutonoma.edificacao.dadosMedidor\"><br><span ng-show=\"itemAlterado.empresaFornecedora.tipoFornecedoraEnum !== null\"><label class=label-component>Tipo do Medidor:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.dadosMedidor[$index].empresaFornecedora.tipoFornecedoraEnum, itemAlterado.empresaFornecedora.tipoFornecedoraEnum) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.empresaFornecedora.tipoFornecedoraEnum.tipoFornecedora }}</span><br></span><span ng-show=\"itemAlterado.codigo !== null\"><label class=label-component>Código do medidor:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.dadosMedidor[$index].codigo, itemAlterado.codigo ) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.codigo }}</span><br></span><span ng-show=\"itemAlterado.empresaFornecedora !== null\"><label class=label-component>Empresa Fornecedora:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.dadosMedidor[$index].empresaFornecedora.nome, itemAlterado.empresaFornecedora.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.empresaFornecedora.nome }}</span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Unidade Autônoma Edificação Dados Medidor \" name=unidadeAutonomaEdificacaoDadosMedidor ng-model=homCtrl.informacoesRecusada.unidadeAutonomaEdificacaoDadosMedidor ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA_EDIFICACAO_DADOS_MEDIDOR)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Documentação</label><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao\"><br><span ng-show=\"itemOriginal.nome !== null\"><label class=label-component>Documento:</label><span class=span-original-component>{{ itemOriginal.nome }}</span><br></span><span ng-show=\"itemOriginal.dataEmissao !== null\"><label class=label-component>Data de Emissão:</label><span class=span-original-component>{{ itemOriginal.dataEmissao | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.dataValidade !== null\"><label class=label-component>Data de Validade:</label><span class=span-original-component>{{ itemOriginal.dataValidade | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.arquivo !== null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.arquivo.url }}\" download=\"{{ itemOriginal.arquivo.nomeOriginal }}\">{{ itemOriginal.arquivo.nomeOriginal }}</a></span><br></span></span></td><td md-cell><div id=unidadeAutonomaEdificacaoDocumentacao><label class=label-titulo>Documentação</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.unidadeAutonoma.edificacao.documentacao\"><br><span ng-show=\"itemAlterado.nome !== null\"><label class=label-component>Documento:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao[$index].nome, itemAlterado.nome) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.nome }}</span><br></span><span ng-show=\"itemAlterado.dataEmissao !== null\"><label class=label-component>Data de Emissão:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao[$index].dataEmissao, itemAlterado.dataEmissao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataEmissao | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.dataValidade !== null\"><label class=label-component>Data de Validade:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao[$index].dataValidade, itemAlterado.dataValidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.dataValidade | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.arquivo !== null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.edificacao.documentacao[$index].arquivo.nome, itemAlterado.arquivo.nome) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.arquivo.url }}\" download=\"{{ itemAlterado.arquivo.nomeOriginal }}\">{{ itemAlterado.arquivo.nomeOriginal }}</a></span><br></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Unidade Autônoma Edificação Documentação\" name=unidadeAutonomaEdificacaoDocumentacao ng-model=homCtrl.informacoesRecusada.unidadeAutonomaEdificacaoDocumentacao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA_EDIFICACAO_DOCUMENTACAO)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-unidade-autonoma/detalhar-imovel-dados-unidade-autonoma-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Dados da Unidade Autônoma</label><br><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem === null\"><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.area !== null\"><label class=label-component>Área Construída (m²):</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.area }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios !== null\"><label class=label-component>Número de Dormitórios:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial != null || homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral != null\"><label class=label-component>Fração ideal do imóvel:</label><span class=span-original-component><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial != null\">{{ homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial }} </span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral != null\">{{ homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral }}</span></span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo !== null\"><label class=label-component>Memorial Descritivo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem !== null\"><label class=label-component>Possui Garagem:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas !== null\"><label class=label-component>Nº de Vagas Cobertas:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas !== null\"><label class=label-component>Nº de Vagas Descobertas:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.observacoes !== null\"><label class=label-component>Observações Gerais:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.observacoes }}</span><br></span></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem !== null\"><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.area !== null\"><label class=label-component>Área (m²):</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.area }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem !== null\"><label class=label-component>Tipo de Garagem:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo !== null\"><label class=label-component>Memorial Descritivo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo }}</span></span></span></td><td md-cell><div id=unidadeAutonoma><label class=label-titulo>Dados da Unidade Autônoma</label><br><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem === null\"><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.area !== null\"><label class=label-component>Área Construída (m²):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.area }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios !== null\"><label class=label-component>Número de Dormitórios:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios, homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial !== null || homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral\"><label class=label-component>Fração ideal do imóvel:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial) ? \'span-alterado-component\' : \'span-original-component\'\" ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial != null\">{{ homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial }} </span><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral) ? \'span-alterado-component\' : \'span-original-component\'\" ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral != null\">{{ homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo !== null\"><label class=label-component>Memorial Descritivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem !== null\"><label class=label-component>Possui Garagem:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem, homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas !== null\"><label class=label-component>Nº de Vagas Cobertas:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas !== null\"><label class=label-component>Nº de Vagas Descobertas:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.observacoes !== null\"><label class=label-component>Observações Gerais:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.observacoes) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.observacoes }}</span><br></span></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem !== null\"><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.area !== null\"><label class=label-component>Área (m²):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.area }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem !== null\"><label class=label-component>Tipo de Garagem:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem.descricao, homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem.descricao }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo !== null\"><label class=label-component>Memorial Descritivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo }}</span></span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Unidade Autonoma\" name=unidadeAutonoma ng-model=homCtrl.informacoesRecusada.unidadeAutonoma ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.unidadeAutonoma.plantas.length != 0 \"><td md-cell ng-show=\"homCtrl.isCadastroAlterado() && homCtrl.imovelOriginal.unidadeAutonoma.plantas.length != 0\"><label class=label-titulo>Plantas da Unidade Autônoma</label><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.unidadeAutonoma.plantas\"><br><span ng-show=\"itemOriginal.descricao !== null\"><label class=label-component>Descrição:</label><span class=span-original-component>{{ itemOriginal.descricao }}</span><br></span><span ng-show=\"itemOriginal.data !== null\"><label class=label-component>Data:</label><span class=span-original-component>{{ itemOriginal.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemOriginal.arquivo !== null\"><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.arquivo.url }}\" download=\"{{ itemOriginal.arquivo.nomeOriginal }}\">{{ itemOriginal.arquivo.nomeOriginal}}</a></span><br></span></span></td><td md-cell><div id=unidadeAutonomaPlantas ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.plantas.length != 0\"><label class=label-titulo>Plantas da Unidade Autônoma</label><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.unidadeAutonoma.plantas\"><br><span ng-show=\"itemAlterado.descricao !== null\"><label class=label-component>Descrição:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.plantas[$index].descricao, itemAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.descricao }}</span><br></span><span ng-show=\"itemAlterado.data !== null\"><label class=label-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.plantas[$index].data, itemAlterado.data) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.data | date: \"dd/MM/yyyy\" }}</span><br></span><span ng-show=\"itemAlterado.arquivo !== null\"><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.plantas[$index].nomeArquivo, itemAlterado.nomeArquivo) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.arquivo.url }}\" download=\"{{ itemAlterado.arquivo.nomeOriginal }}\">{{ itemAlterado.arquivo.nomeOriginal }}</a></span><br></span></span></div></td><td md-cell class=coluna-recusar ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.plantas.length != 0\"><md-checkbox aria-label=\"Recusar Plantas Unidade Autônoma\" name=unidadeAutonomaPlantas ng-model=homCtrl.informacoesRecusada.unidadeAutonomaPlantas ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.UNIDADE_AUTONOMA_PLANTAS)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><div style=\"margin-top: 5px\"><label class=label-titulo>Fotos da Unidade Autônoma</label><br><span><inc-modal-fotos titulo-modal=\"Fotos da Unidade Autônoma\" fotos=homCtrl.imovelOriginal.unidadeAutonoma.fotos></inc-modal-fotos></span></div></td><td md-cell><div style=\"margin-top: 5px\"><div id=fotosUnidadeAutonoma><label class=label-titulo style=\"margin-top: 5px\">Fotos da Unidade Autônoma</label><br><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fotos , homCtrl.imovelAlterado.unidadeAutonoma.fotos) ? \'span-alterado-component\' : \'span-original-component\'\"><inc-modal-fotos titulo-modal=\"Fotos da Unidade Autônoma\" fotos=homCtrl.imovelAlterado.unidadeAutonoma.fotos></inc-modal-fotos></span></div></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Fotos do Imóvel\" name=fotosUnidadeAutonoma ng-model=homCtrl.informacoesRecusada.fotosUnidadeAutonoma ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.FOTOS_UNIDADE_AUTONOMA)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-unidade-autonoma/detalhar-imovel-dados-unidade-autonoma.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados da Unidade Autônoma</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Área Construída (m²)</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.area }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.area }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área construída\" name=areaConstruidaUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area)\" ng-checked=\"homCtrl.exists(\'areaConstruidaUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número de Dormitórios</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios, homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número de dormitórios\" name=numeroDormitoriosUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.numeroDormitorios, homCtrl.imovelAlterado.unidadeAutonoma.numeroDormitorios)\" ng-checked=\"homCtrl.exists(\'numeroDormitoriosUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Memorial Descritivo</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo\" name=memorialDescritivoUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Possui Garagem</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.boolToString(homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem) }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem, homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem) ? \'dado-alterado\' : \'\'\">{{ homCtrl.boolToString(homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem) }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar posse garagem\" name=possuiGaragemUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.possuiGaragem, homCtrl.imovelAlterado.unidadeAutonoma.possuiGaragem)\" ng-checked=\"homCtrl.exists(\'possuiGaragemUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número de Vagas Cobertas</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número de vagas cobertas\" name=numeroVagasCobertasUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasCobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasCobertas)\" ng-checked=\"homCtrl.exists(\'numeroVagasCobertasUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Número de Vagas Descobertas</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar número de vagas descobertas\" name=numeroVagasDescobertasUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.numeroVagasDescobertas, homCtrl.imovelAlterado.unidadeAutonoma.numeroVagasDescobertas)\" ng-checked=\"homCtrl.exists(\'numeroVagasDescobertasUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Observações Gerais</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.observacoes }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.observacoes) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.observacoes }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar observações\" name=observacoesUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.observacoes, homCtrl.imovelAlterado.unidadeAutonoma.observacoes)\" ng-checked=\"homCtrl.exists(\'observacoesUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=3>Fotos da Unidade Autônoma</th></tr><tr md-row><th md-column width=45%>Original</th><th md-column width=45%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell><span ng-show=homCtrl.isCadastroAlterado()><inc-modal-fotos titulo-modal=\"Fotos da Unidade Autônoma\" fotos=homCtrl.imovelOriginal.unidadeAutonoma.fotos></inc-modal-fotos></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fotos, homCtrl.imovelAlterado.unidadeAutonoma.fotos) ? \'dado-alterado\' : \'\'\"><inc-modal-fotos titulo-modal=\"Fotos da Unidade Autônoma\" fotos=homCtrl.imovelAlterado.unidadeAutonoma.fotos></inc-modal-fotos></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fotos da unidade autônoma\" name=fotosUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.fotos, homCtrl.imovelAlterado.unidadeAutonoma.fotos)\" ng-checked=\"homCtrl.exists(\'fotosUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Plantas da Unidade Autônoma</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Plantas</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"planta in homCtrl.imovelOriginal.unidadeAutonoma.plantas\"><a ng-href=\"{{ planta.arquivo.url }}\" download=\"{{ planta.arquivo.nomeOriginal }}\">{{ planta.arquivo.nomeOriginal }}</a></span></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.plantas, homCtrl.imovelAlterado.unidadeAutonoma.plantas) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"planta in homCtrl.imovelAlterado.unidadeAutonoma.plantas\"><a ng-href=\"{{ planta.arquivo.url }}\" download=\"{{ planta.arquivo.nomeOriginal }}\">{{ planta.arquivo.nomeOriginal }}</a></span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar plantas da unidade autônoma\" name=plantasUA ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.plantas, homCtrl.imovelAlterado.unidadeAutonoma.plantas)\" ng-checked=\"homCtrl.exists(\'plantasUA\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-homologar><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados da Garagem</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30%>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell>Área (m²)</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.area }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.area }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área da garagem\" name=areaGaragem ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.area, homCtrl.imovelAlterado.unidadeAutonoma.area)\" ng-checked=\"homCtrl.exists(\'areaGaragem\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Tipo de Garagem</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem.descricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem.id, homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem.id) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem.descricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tipo de garagem\" name=tipoGaragem ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.tipoGaragem.id, homCtrl.imovelAlterado.unidadeAutonoma.tipoGaragem.id)\" ng-checked=\"homCtrl.exists(\'tipoGaragem\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row><td md-cell>Memorial Descritivo</td><td md-cell><span ng-show=homCtrl.isCadastroAlterado()>{{ homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo da garagem\" name=memorialDescritivoGaragem ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.memorialDescritivo, homCtrl.imovelAlterado.unidadeAutonoma.memorialDescritivo)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoGaragem\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-terreno/detalhar-imovel-dados-terreno-novo.tmpl.html","<div layout=column><md-table-container></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column width=50% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=50%>Informação Nova/Alterada</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Dados Cartoriais</label><br><span ng-show=\"homCtrl.imovelOriginal.terreno.tipoGeometriaCartorial !== null\"><label class=label-component>Geometria não-geoferenciada:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.tipoGeometriaCartorial === \"NAO_GEORREFERENCIADA\" ? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-component>Arquivo (zip,csv e txt):</label><span class=span-original-component><a ng-href=\"{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.url }}\" download=\"{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.nome }}\">{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.nome }}</a></span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Data:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.dataPlanta | date: \"dd/MM/yyyy\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Escala de Vetorização:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.escala}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Forma de Levantamento:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.tipoFormaLevantamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Resolução Espacial (m):</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.resolucaoEspacial}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Tipo de Equipamento Utilizado:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.tipoEquipamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial !== null\"><label class=label-component>Memorial Descritivo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial !== null\"><label class=label-component>Unidade de Medida da Área:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial.unidade }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCartorial !== null\"><label class=label-component>Área da Geometria do Terreno:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCartorial.areaGeometria}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.areaTotalCartorial !== null\"><label class=label-component>Área Total:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.areaTotalCartorial|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.areaUniaoCartorial !== null\"><label class=label-component>Área da União:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.areaUniaoCartorial|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao !== null\"><label class=label-component>Memorial Descritivo da Área da União:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao }}</span></span></td><td md-cell><div id=dadosCartoriais><label class=label-titulo>Dados Cartoriais</label><br><span ng-show=\"homCtrl.imoveAlterado.terreno.tipoGeometriaCartorial !== null\"><label class=label-component>Geometria não-geoferenciada:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imoveOriginal.terreno.tipoGeometriaCartorial, homCtrl.imoveAlterado.terreno.tipoGeometriaCartorial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imoveAlterado.terreno.tipoGeometriaCartorial === \"NAO_GEORREFERENCIADA\" ? \"Sim\" : \"Não\" }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-component>Arquivo (zip,csv e txt):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.nome, homCtrl.imovelAlterado.terreno.arquivoCartorial.nome) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.url }}\" download=\"{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.nome }}\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.nome }}</a></span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.dataPlanta, homCtrl.imovelAlterado.terreno.arquivoCartorial.dataPlanta) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.dataPlanta | date: \"dd/MM/yyyy\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Escala de Vetorização:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.escala, homCtrl.imovelAlterado.terreno.arquivoCartorial.escala) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.escala}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Forma de Levantamento:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.tipoFormaLevantamento.descricao, homCtrl.imovelAlterado.terreno.arquivoCartorial.tipoFormaLevantamento.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.tipoFormaLevantamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Resolução Espacial (m):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.resolucaoEspacial, homCtrl.imovelAlterado.terreno.arquivoCartorial.resolucaoEspacial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.resolucaoEspacial}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-sub-component>Tipo de Equipamento Utilizado:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.tipoEquipamento.descricao, homCtrl.imovelAlterado.terreno.arquivoCartorial.tipoEquipamento.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.tipoEquipamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial !== null\"><label class=label-component>Memorial Descritivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial, homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial !== null\"><label class=label-component>Unidade de Medida da Área:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial.unidade, homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.unidade) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.unidade }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCartorial !== null\"><label class=label-component>Área da Geometria do Terreno:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCartorial.areaGeometria, homCtrl.imovelAlterado.terreno.arquivoCartorial.areaGeometria) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCartorial.areaGeometria}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.areaTotalCartorial !== null\"><label class=label-component>Área Total:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaTotalCartorial, homCtrl.imovelAlterado.terreno.areaTotalCartorial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.areaTotalCartorial|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.areaUniaoCartorial !== null\"><label class=label-component>Área da União:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaUniaoCartorial, homCtrl.imovelAlterado.terreno.areaUniaoCartorial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.areaUniaoCartorial|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao !== null\"><label class=label-component>Memorial Descritivo da Área da União:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao,  homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao }}</span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Dados Cartoriais\" name=dadosCartoriais ng-model=homCtrl.informacoesRecusada.dadosCartoriais ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.DADOS_CARTORIAIS)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Dados Cadastrais</label><br><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-component>Arquivo (zip,csv e txt):</label><span class=span-original-component><a ng-href=\"{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.url }}\" download=\"{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.nome }}\">{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.nome }}</a></span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Data:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.dataPlanta | date: \"dd/MM/yyyy\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Escala de Vetorização:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.escala}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Forma de Levantamento:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.tipoFormaLevantamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Resolução Espacial (m):</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.resolucaoEspacial}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Tipo de Equipamento Utilizado:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.tipoEquipamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral !== null\"><label class=label-component>Memorial Descritivo:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.unidadeMedidaCadastral !== null\"><label class=label-component>Unidade de Medida da Área:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.unidadeMedidaCadastral }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.arquivoCadastral !== null\"><label class=label-component>Área da Geometria do Terreno:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.arquivoCadastral.areaGeometria}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.areaTotalCadastral !== null\"><label class=label-component>Área Total:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.areaTotalCadastral|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.areaUniaoCadastral !== null\"><label class=label-component>Área da União:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.areaUniaoCadastral|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao !== null\"><label class=label-component>Memorial Descritivo da Área da União:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao }}</span></span></td><td md-cell><div id=dadosCadastrais><label class=label-titulo>Dados Cadastrais</label><br><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-component>Arquivo (zip,csv e txt):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.nome, homCtrl.imovelAlterado.terreno.arquivoCadastral.nome) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.url }}\" download=\"{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.nome }}\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.nome }}</a></span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.dataPlanta, homCtrl.imovelAlterado.terreno.arquivoCadastral.dataPlanta) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.dataPlanta | date: \"dd/MM/yyyy\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Escala de Vetorização:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.escala, homCtrl.imovelAlterado.terreno.arquivoCadastral.escala) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.escala }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Forma de Levantamento:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.tipoFormaLevantamento.descricao, homCtrl.imovelAlterado.terreno.arquivoCadastral.tipoFormaLevantamento.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.tipoFormaLevantamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Resolução Espacial (m):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.resolucaoEspacial, homCtrl.imovelAlterado.terreno.arquivoCadastral.resolucaoEspacial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.resolucaoEspacial}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-sub-component>Tipo de Equipamento Utilizado:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.tipoEquipamento.descricao, homCtrl.imovelAlterado.terreno.arquivoCadastral.tipoEquipamento.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.tipoEquipamento.descricao}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral !== null\"><label class=label-component>Memorial Descritivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral, homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral !== null\"><label class=label-component>Unidade de Medida da Área:</label><span ng-class=\"homCtrl.isDadoDivergente() ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.arquivoCadastral !== null\"><label class=label-component>Área da Geometria do Terreno:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivoCadastral.areaGeometria, homCtrl.imovelAlterado.terreno.arquivoCadastral.areaGeometria) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.arquivoCadastral.areaGeometria}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.areaTotalCadastral !== null\"><label class=label-component>Área Total:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaTotalCadastral, homCtrl.imovelAlterado.terreno.areaTotalCadastral) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.areaTotalCadastral|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.areaUniaoCadastral !== null\"><label class=label-component>Área da União:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaUniaoCadastral, homCtrl.imovelAlterado.terreno.areaUniaoCadastral) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.areaUniaoCadastral|number:2 }}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao !== null\"><label class=label-component>Memorial Descritivo da Área da União:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao,  homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao }}</span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar Dados Cadastrais\" name=dadosCadastrais ng-model=homCtrl.informacoesRecusada.dadosCadastrais ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.DADOS_CADASTRAIS)></md-checkbox></td></tr><tr md-row><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Restriçoes de Uso/Ocupação</label><br><span ng-show=\"homCtrl.imovelOriginal.terreno.isAreaInteresseSocial !== null\"><label class=label-component>Zona/Área de Interesse Social (ZEIS):</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.isAreaInteresseSocial ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.isAreaSeguranca !== null\"><label class=label-component>Situado em área de segurança:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.isAreaSeguranca ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.isAreaNonAedificanti !== null\"><label class=label-component>Área non Aedificandi:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.isAreaNonAedificandi ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.isPatrimonioTombado !== null\"><label class=label-component>Tombado como patrimônio histórico, artístico e/ou cultural:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.isPatrimonioTombado ? homCtrl.imovelOriginal.terreno.descricaoPatrimonioTombado : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelOriginal.terreno.isOutraRestricao !== null\"><label class=label-component>Outra Restrição de uso/ocupação:</label><span class=span-original-component>{{ homCtrl.imovelOriginal.terreno.isOutraRestricao ? homCtrl.imovelOriginal.terreno.descricaoOutraRestricao : \"Não\"}}</span></span></td><td md-cell><div id=restricoesUsoOcupacao><label class=label-titulo>Restriçoes de Uso/Ocupação</label><br><span ng-show=\"homCtrl.imovelAlterado.terreno.isAreaInteresseSocial !== null\"><label class=label-component>Zona/Área de Interesse Social (ZEIS):</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaInteresseSocial, homCtrl.imovelAlterado.terreno.isAreaInteresseSocial) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.isAreaInteresseSocial ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.isAreaSeguranca !== null\"><label class=label-component>Situado em área de segurança:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaSeguranca, homCtrl.imovelAlterado.terreno.isAreaSeguranca) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.isAreaSeguranca ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.isAreaNonAedificanti !== null\"><label class=label-component>Área non Aedificandi:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaNonAedificanti, homCtrl.imovelAlterado.terreno.isAreaNonAedificanti) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.isAreaNonAedificandi ? \"Sim\" : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.isPatrimonioTombado !== null\"><label class=label-component>Tombado como patrimônio histórico, artístico e/ou cultural:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isPatrimonioTombado, homCtrl.imovelAlterado.terreno.isPatrimonioTombado) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.isPatrimonioTombado ? homCtrl.imovelAlterado.terreno.descricaoPatrimonioTombado : \"Não\"}}</span><br></span><span ng-show=\"homCtrl.imovelAlterado.terreno.isOutraRestricao !== null\"><label class=label-component>Outra Restrição de uso/ocupação:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isOutraRestricao, homCtrl.imovelAlterado.terreno.isOutraRestricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ homCtrl.imovelAlterado.terreno.isOutraRestricao ? homCtrl.imovelAlterado.terreno.descricaoOutraRestricao : \"Não\"}}</span></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=Recusar name=restricoesUsoOcupacao ng-model=homCtrl.informacoesRecusada.restricoesUsoOcupacao ng-click=homCtrl.toggle(homCtrl.CONSTANTES_IMOVEL_RECUSADO.RESTRICOES_USO_OCUPACAO)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelOriginal.arquivosComplementaresLista.length !== 0 || homCtrl.imovelAlterado.arquivosComplementaresLista.length !== 0\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><label class=label-titulo>Arquivos Complementares</label><br><span ng-repeat=\"itemOriginal in homCtrl.imovelOriginal.arquivosComplementaresLista\"><label class=label-component>Descrição:</label><span class=span-original-component>{{ itemOriginal.descricao }}</span><br><label class=label-component>Data:</label><span class=span-original-component>{{ itemOriginal.data | date: \"dd/MM/yyyy\" }}</span><br><label class=label-component>Arquivo:</label><span class=span-original-component><a ng-href=\"{{ itemOriginal.arquivo.url }}\" download=\"{{ itemOriginal.arquivo.nomeOriginal }}\">{{ itemOriginal.arquivo.nomeOriginal }}</a></span><br><br></span></td><td md-cell><div id=arquivosComplementaresTerreno><label class=label-titulo>Arquivos Complementares</label><br><span ng-repeat=\"itemAlterado in homCtrl.imovelAlterado.arquivosComplementaresLista\"><label class=label-component>Descrição:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.arquivosComplementaresLista[$index].descricao, itemAlterado.descricao) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.descricao }}</span><br><label class=label-component>Data:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.arquivosComplementaresLista[$index].data, itemAlterado.data) ? \'span-alterado-component\' : \'span-original-component\'\">{{ itemAlterado.data | date: \"dd/MM/yyyy\" }}</span><br><label class=label-component>Arquivo:</label><span ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.arquivosComplementaresLista[$index].arquivo.nomeOriginal, itemAlterado.arquivo.nomeOriginal) ? \'span-alterado-component\' : \'span-original-component\'\"><a ng-href=\"{{ itemAlterado.arquivo.url }}\" download=\"{{ itemAlterado.arquivo.nomeOriginal }}\">{{ itemAlterado.arquivo.nomeOriginal }}</a></span><br><br></span></div></td><td md-cell class=coluna-recusar><md-checkbox aria-label=Recusar name=arquivosComplementaresTerreno ng-model=homCtrl.informacoesRecusada.arquivosComplementaresTerreno ng-click=homCtrl.toggle(vm.CONSTANTES_IMOVEL_RECUSADO.ARQUIVOS_COMPLEMENTARES_TERRENO)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/pages/pendencia/pendencia-mapa/view/componentes-terreno/detalhar-imovel-dados-terreno.tmpl.html","<div layout=column><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column class=table-title colspan=3>Geometria Cartorial do Terreno</th></tr><tr md-row><th md-column width=45%; ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=45%;>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.geometriaTerrenoCartorial != null\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><inc-mapa-pendencia geometria=homCtrl.imovelOriginal.terreno.geometriaTerrenoCartorial></inc-mapa-pendencia></td><td md-cell><inc-mapa-pendencia geometria=homCtrl.imovelAlterado.terreno.geometriaTerrenoCartorial dado-alterado=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.geometriaTerrenoCartorial.coordinates, homCtrl.imovelAlterado.terreno.geometriaTerrenoCartorial.coordinates)\"></inc-mapa-pendencia></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar geometria cartorial\" name=geometriaCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.geometriaTerrenoCartorial.coordinates, homCtrl.imovelAlterado.terreno.geometriaTerrenoCartorial.coordinates)\" ng-checked=\"homCtrl.exists(\'geometriaCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados de Cartoriais</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"typeof(homCtrl.imovelAlterado.terreno.isCertificaoIncra) != \'undefined\'\"><td md-cell>Levantamento Cadastral Certificado pelo Incra</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isCertificaoIncra ? \"Sim\" : \"Não\" }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isCertificaoIncra, homCtrl.imovelAlterado.terreno.isCertificaoIncra) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isCertificaoIncra ? \"Sim\" : \"Não\" }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar certificação Incra\" name=certificadoPeloIncra ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isCertificaoIncra, homCtrl.imovelAlterado.terreno.isCertificaoIncra)\" ng-checked=\"homCtrl.exists(\'certificadoPeloIncra\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial != null\"><td md-cell>Memorial Descritivo</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial, homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo cartorial\" name=memorialDescritivoCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorial, homCtrl.imovelAlterado.terreno.memorialDescritivoCartorial)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.unidade != null\"><td md-cell>Unidade de Medida da Área</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial.unidade }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial.name, homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.name) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.unidade }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar unidade de medida da área cartorial\" name=unidadeMedidaCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.unidadeMedidaCartorial.name, homCtrl.imovelAlterado.terreno.unidadeMedidaCartorial.name)\" ng-checked=\"homCtrl.exists(\'unidadeMedidaCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.areaTotalCartorial != null\"><td md-cell>Área Total(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.areaTotalCartorial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaTotalCartorial, homCtrl.imovelAlterado.terreno.areaTotalCartorial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.areaTotalCartorial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área total cartorial\" name=areaTotalCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.areaTotalCartorial, homCtrl.imovelAlterado.terreno.areaTotalCartorial)\" ng-checked=\"homCtrl.exists(\'areaTotalCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.areaUniaoCartorial != null\"><td md-cell>Área da União(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.areaUniaoCartorial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaUniaoCartorial, homCtrl.imovelAlterado.terreno.areaUniaoCartorial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.areaUniaoCartorial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área da união cartorial\" name=areaUniaoCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.areaUniaoCartorial, homCtrl.imovelAlterado.terreno.areaUniaoCartorial)\" ng-checked=\"homCtrl.exists(\'areaUniaoCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial != null\"><td md-cell>Fração Ideal do Imóvel(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fração ideal do imóvel cartorial\" name=fracaoIdealCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCartorial, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCartorial)\" ng-checked=\"homCtrl.exists(\'fracaoIdealCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCartorial != null\"><td md-cell>Fração Ideal da União(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCartorial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCartorial, homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCartorial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCartorial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fração ideal da união cartorial\" name=fracaoIdealUniaoCartorial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCartorial, homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCartorial)\" ng-checked=\"homCtrl.exists(\'fracaoIdealUniaoCartorial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao != null\"><td md-cell>Memorial Descritivo da Área da União</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao, homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo da união cartorial\" name=memorialDescritivoCartorialUniao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.memorialDescritivoCartorialUniao, homCtrl.imovelAlterado.terreno.memorialDescritivoCartorialUniao)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoCartorialUniao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column class=table-title colspan=3>Geometria Cadastral do Terreno</th></tr><tr md-row><th md-column width=45%; ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=45%;>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.geometriaTerreno != null\"><td md-cell ng-show=homCtrl.isCadastroAlterado()><inc-mapa-pendencia geometria=homCtrl.imovelOriginal.terreno.geometriaTerreno></inc-mapa-pendencia></td><td md-cell><inc-mapa-pendencia geometria=homCtrl.imovelAlterado.terreno.geometriaTerreno dado-alterado=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.geometriaTerreno.coordinates, homCtrl.imovelAlterado.terreno.geometriaTerreno.coordinates)\"></inc-mapa-pendencia></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar geometria cadastral\" name=geometriaCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.geometriaTerreno.coordinates, homCtrl.imovelAlterado.terreno.geometriaTerreno.coordinates)\" ng-checked=\"homCtrl.exists(\'geometriaCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column class=table-title colspan=4>Dados de Cadastrais</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral != null\"><td md-cell>Memorial Descritivo</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral, homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo cadastral\" name=memorialDescritivoCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastral, homCtrl.imovelAlterado.terreno.memorialDescritivoCadastral)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral != null\"><td md-cell>Unidade de Medida da Área</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.unidadeMedidaCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.unidadeMedidaCadastral.name, homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral.name) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar unidade de medida da área cadastral\" name=unidadeMedidaCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.unidadeMedidaCadastral.name, homCtrl.imovelAlterado.terreno.unidadeMedidaCadastral.name)\" ng-checked=\"homCtrl.exists(\'unidadeMedidaCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.areaTotalCadastral != null\"><td md-cell>Área Total(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.areaTotalCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaTotalCadastral, homCtrl.imovelAlterado.terreno.areaTotalCadastral) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.areaTotalCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área total cadastral\" name=areaTotalCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.areaTotalCadastral, homCtrl.imovelAlterado.terreno.areaTotalCadastral)\" ng-checked=\"homCtrl.exists(\'areaTotalCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.areaUniaoCadastral != null\"><td md-cell>Área da União(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.areaUniaoCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.areaUniaoCadastral, homCtrl.imovelAlterado.terreno.areaUniaoCadastral) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.areaUniaoCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área da união cadastral\" name=areaUniaoCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.areaUniaoCadastral, homCtrl.imovelAlterado.terreno.areaUniaoCadastral)\" ng-checked=\"homCtrl.exists(\'areaUniaoCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral != null\"><td md-cell>Fração Ideal do Imóvel(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fração ideal do imóvel cadastral\" name=fracaoIdealCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.unidadeAutonoma.fracaoIdealCadastral, homCtrl.imovelAlterado.unidadeAutonoma.fracaoIdealCadastral)\" ng-checked=\"homCtrl.exists(\'fracaoIdealCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCadastral != null\"><td md-cell>Fração Ideal da União(m²)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCadastral }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCadastral, homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCadastral) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCadastral }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar fração ideal da união cadastral\" name=fracaoIdealUniaoCadastral ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.fracaoIdealUniaoCadastral, homCtrl.imovelAlterado.terreno.fracaoIdealUniaoCadastral)\" ng-checked=\"homCtrl.exists(\'fracaoIdealUniaoCadastral\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao != null\"><td md-cell>Memorial Descritivo da Área da União</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao, homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar memorial descritivo da união cadastral\" name=memorialDescritivoCadastralUniao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.memorialDescritivoCadastralUniao, homCtrl.imovelAlterado.terreno.memorialDescritivoCadastralUniao)\" ng-checked=\"homCtrl.exists(\'memorialDescritivoCadastralUniao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column class=table-title colspan=4>Restrições de Uso/Ocupação</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.isAreaInteresseSocial != null\"><td md-cell>Zona/Área de Interesse Social (ZEIS)</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isAreaInteresseSocial }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaInteresseSocial, homCtrl.imovelAlterado.terreno.isAreaInteresseSocial) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isAreaInteresseSocial }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área de interesse social\" name=areaInteresseSocial ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isAreaInteresseSocial, homCtrl.imovelAlterado.terreno.isAreaInteresseSocial)\" ng-checked=\"homCtrl.exists(\'areaInteresseSocial\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.isAreaSeguranca != null\"><td md-cell>Situado em Área de Segurança</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isAreaSeguranca }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaSeguranca, homCtrl.imovelAlterado.terreno.isAreaSeguranca) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isAreaSeguranca }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área de segurança\" name=areaSeguranca ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isAreaSeguranca, homCtrl.imovelAlterado.terreno.isAreaSeguranca)\" ng-checked=\"homCtrl.exists(\'areaSeguranca\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.isPatrimonioTombado != null\"><td md-cell>Tombado Como Patrimônio Histórico, Artístico e/ou Cultural</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isPatrimonioTombado }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isPatrimonioTombado, homCtrl.imovelAlterado.terreno.isPatrimonioTombado) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isPatrimonioTombado }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar tombado como patrimônio\" name=patrimonioTombado ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isPatrimonioTombado, homCtrl.imovelAlterado.terreno.isPatrimonioTombado)\" ng-checked=\"homCtrl.exists(\'patrimonioTombado\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.isAreaNonAedificandi != null\"><td md-cell>Área Non Aedificandi</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isAreaNonAedificandi }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isAreaNonAedificandi, homCtrl.imovelAlterado.terreno.isAreaNonAedificandi) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isAreaNonAedificandi }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar área non aedificandi\" name=areaNonAedificandi ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isAreaNonAedificandi, homCtrl.imovelAlterado.terreno.isAreaNonAedificandi)\" ng-checked=\"homCtrl.exists(\'areaNonAedificandi\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr><tr md-row ng-show=\"homCtrl.imovelAlterado.terreno.isOutraRestricao != null\"><td md-cell>Outra Restrição de Uso/Ocupação</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span>{{ homCtrl.imovelOriginal.terreno.isOutraRestricao }}</span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.isOutraRestricao, homCtrl.imovelAlterado.terreno.isOutraRestricao) ? \'dado-alterado\' : \'\'\">{{ homCtrl.imovelAlterado.terreno.isOutraRestricao }}</td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar outra restrição\" name=outraRestricao ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.isOutraRestricao, homCtrl.imovelAlterado.terreno.isOutraRestricao)\" ng-checked=\"homCtrl.exists(\'outraRestricao\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container><md-table-container><table md-table class=tabela-pendencia><thead md-head><tr md-row><th md-column class=table-title colspan=4>Arquivos Complementares</th></tr><tr md-row><th md-column width=30%>Campo</th><th md-column width=30% ng-show=homCtrl.isCadastroAlterado()>Original</th><th md-column width=30%>Alterado/Novo</th><th md-column>Recusar</th></tr></thead><tbody md-body><tr md-row ng-show=\"homCtrl.imovelOriginal.arquivosComplementaresLista.length > 0 || homCtrl.imovelAlterado.arquivosComplementaresLista.length > 0\"><td md-cell>Arquivos Complementares</td><td md-cell ng-show=homCtrl.isCadastroAlterado()><span ng-repeat=\"arquivoComplementar in homCtrl.imovelOriginal.arquivosComplementaresLista\"><a ng-href=\"{{ arquivoComplementar.arquivo.url }}\" download=\"{{ arquivoComplementar.arquivo.nomeOriginal }}\">{{ arquivoComplementar.arquivo.nomeOriginal }}</a></span></td><td md-cell ng-class=\"homCtrl.isDadoDivergente(homCtrl.imovelOriginal.terreno.arquivosComplementares, homCtrl.imovelAlterado.terreno.arquivosComplementares) ? \'dado-alterado\' : \'\'\"><span ng-repeat=\"arquivoComplementar in homCtrl.imovelAlterado.arquivosComplementaresLista\"><a ng-href=\"{{ arquivoComplementar.arquivo.url }}\" download=\"{{ arquivoComplementar.arquivo.nomeOriginal }}\">{{ arquivoComplementar.arquivo.nomeOriginal }}</a></span></td><td md-cell class=coluna-recusar><md-checkbox aria-label=\"Recusar arquivos complementares\" name=arquivosComplementares ng-disabled=\"homCtrl.desabilitarCheckbox(homCtrl.imovelOriginal.terreno.arquivosComplementares, homCtrl.imovelAlterado.terreno.arquivosComplementares)\" ng-checked=\"homCtrl.exists(\'arquivosComplementares\')\" ng-click=homCtrl.toggle($event.currentTarget)></md-checkbox></td></tr></tbody></table></md-table-container></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/dados-gerais-imovel/fieldset-matricula-transcricao/view/matricula-transcricao-fieldset.html","<fieldset class=fieldset><legend><label style=\"color: rgb(76, 175, 80)\">Matrícula/Transcrição</label></legend><div ng-if=!isDetail><ng-form name=forms.matriculaForm isolate-form><div layout-gt-xs=row><!-- UF --><md-input-container class=\"md-block padding-padrao-interface margem-baixa-padrao\" flex-gt-sm flex ng-if=isPaisBrasil()><label id=ufMatriculaLabel>UF</label><!--<md-select ng-required=\"isPaisBrasil()\"\n                                   campo-ajuda=\"INC_FORM_DADOS_GERAIS_UF_CARTORIO\"\n                                   class=\"secaoDadosLocalizacaoImovel\"\n                                   ng-model=\"matricula.uf\"\n                                   ng-model-options=\"{trackBy: \'$value\'}\"\n                                   name=\"ufSelect\"\n                                   placeholder=\"UF\"\n                                   ng-change=\"pesquisaMunicipio(matricula.uf)\">\n                            <md-option ng-repeat=\"uf in ufs\" ng-value=\"uf.sigla\">\n                                {{uf.sigla}}\n                            </md-option>\n                        </md-select>--><md-autocomplete campo-ajuda=INC_FORM_DADOS_GERAIS_UF_CARTORIO class=secaoDadosLocalizacaoImovel required name=ufMatricula md-clear-button=!$mdAutocompleteCtrl.isDisabled md-selected-item=matricula.uf md-search-text=searchUf md-items=\"uf in ufs | filtro : \'sigla\' : searchUf | orderByBr:\'sigla\'\" md-selected-item-change=changeUF(matricula.uf.sigla) md-item-text=uf.sigla md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=ufMatriculaLabel><md-item-template><span md-highlight-text=searchUf md-highlight-flags=^i>{{uf.sigla}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{searchUf}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><!-- Município --><md-input-container class=\"md-block padding-padrao-interface margem-top-padrao margem-baixa-padrao\" flex-gt-sm flex ng-if=isPaisBrasil()><label id=municipioMatriculaLabel>Município</label><md-autocomplete md-no-cache=true campo-ajuda=INC_FORM_DADOS_GERAIS_MUNICIPIO_CARTORIO class=secaoDadosLocalizacaoImovel required name=municipioMatricula md-clear-button=!$mdAutocompleteCtrl.isDisabled md-selected-item=matricula.municipio md-search-text=searchMunicipioMatricula md-items=\"municipio in municipios | filtro: \'nome\' : searchMunicipioMatricula | orderByBr:\'nome\'\" md-selected-item-change=carregarCartorios(matricula) md-item-text=municipio.nome md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=municipioMatriculaLabel><md-item-template><span md-highlight-text=searchMunicipioMatricula md-highlight-flags=^i>{{municipio.nome}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{searchMunicipioMatricula}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><!-- Nome do Cartório --><md-input-container class=md-block flex-gt-sm flex ng-if=\"(isPaisBrasil() && isSituacaoIncorporacaoIncorporado()) || (isPaisBrasil() && !isSituacaoIncorporacaoIncorporado())\"><label id=cartorioMatriculaLabel>Nome do Cartório</label><!--<md-select ng-required=\"isPaisBrasil()\"\n                                   campo-ajuda=\"INC_FORM_DADOS_GERAIS_NOME_CARTORIO\"\n                                   class=\"secaoDadosLocalizacaoImovel\"\n                                   ng-model=\"matricula.cartorio\"\n                                   ng-model-options=\"{trackBy: \'$value.id\'}\"\n                                   name=\"cartorio\">\n                            <md-option ng-repeat=\"cartorio in cartorios | filter:nome | orderBy:\'nome\'\"\n                                       ng-value=\"cartorio\">\n                                {{cartorio.nome}}\n                            </md-option>\n                        </md-select>--><md-autocomplete md-no-cache=true campo-ajuda=INC_FORM_DADOS_GERAIS_NOME_CARTORIO class=secaoDadosLocalizacaoImovel required name=cartorioMatricula md-clear-button=!$mdAutocompleteCtrl.isDisabled md-selected-item=matricula.cartorio md-search-text=$scope.searchCartorioMatricula md-items=\"cartorio in cartorios | filter:{nome:$scope.searchCartorioMatricula} | orderByBr:\'nome\'\" md-item-text=cartorio.nome md-min-length=0 md-escape-options=clear md-select-on-match md-match-case-insensitive placeholder=\"Informe o dado\" input-aria-labelledby=cartorioMatriculaLabel><md-item-template><span md-highlight-text=$scope.searchCartorioMatricula md-highlight-flags=^i>{{cartorio.nome}}</span></md-item-template><md-not-found>Nenhum resultado para \"{{$scope.searchCartorioMatricula}}\" foi encontrado.</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block flex-gt-sm flex ng-if=!isPaisBrasil()><label>Identificação do Cartório</label><textarea campo-ajuda=INC_FORM_DADOS_GERAIS_ID_CARTORIO class=secaoDadosLocalizacaoImovel ng-model=matricula.cartorioEstrangeiro md-maxlength=400></textarea></md-input-container></div><!-- Linha das Datas --><div layout-gt-xs=row class=layout-gt-xs-row><md-input-container flex-gt-xs class=\"flex-gt-xs padding-padrao-interface margem-top-padrao margem-baixa-padrao\"><label>Matrícula/Transcrição</label><input campo-ajuda=INC_FORM_DADOS_GERAIS_MATRIC_TRANSC required class=secaoDadosIncorporacao ng-model=matricula.numero name=numeroMatricula></md-input-container><md-input-container flex-gt-xs class=\"flex-gt-xs margem-top-padrao margem-baixa-padrao\"><label>Data de Registro</label><md-datepicker ng-mask=39/19/9999 restrict=reject ng-required=isSituacaoIncorporacaoIncorporado() campo-ajuda=INC_FORM_DADOS_GERAIS_DT_REGISTRO class=secaoDadosIncorporacao ng-model=matricula.dataRegistro md-max-date=hoje name=dataRegistro></md-datepicker></md-input-container></div><div layout=row layout-align=\"end center\"><md-button class=\"md-primary md-raised\" ng-click=adicionaMatricula(matricula) ng-show=!isNovaMatricula(matricula)>Salvar</md-button><md-button class=\"md-primary md-raised primeira-maiuscula\" ng-click=adicionaMatricula(matricula) ng-show=isNovaMatricula(matricula)>Adicionar</md-button></div></ng-form></div><!-- Tabela Template --><div ng-show=\"hasMatricula() || isDetalhar()\"><md-table-container><table md-table><thead md-head md-order=ordenacao><tr md-row><th md-column>Localização</th><th md-column md-order-by=cartorio.nome>Cartório</th><th md-column md-order-by=numero>Matrícula/Transcrição</th><th md-column md-order-by=dataRegistro>Data do Registro</th><th md-column ng-show=!isDetail>Ações</th></tr></thead><tbody md-body><tr md-row ng-show=\"imovel.incorporacao.matriculas.length === 0\"><td md-cell colspan=6>Nenhum Registro</td></tr><tr md-row ng-repeat=\"matricula in imovel.incorporacao.matriculas | orderBy: ordenacao | limitTo: paginacaoMatriculas.limit: (paginacaoMatriculas.page - 1) * paginacaoMatriculas.limit\"><td md-cell ng-class=\"isPaisBrasil() ? \'\' : \'md-cell-null\'\">{{ isPaisBrasil() ? matricula.municipio + \"-\" + matricula.uf : \"-\" }}</td><td md-cell ng-show=isPaisBrasil()>{{ matricula.cartorio.nome }}</td><td md-cell ng-show=!isPaisBrasil()>{{ matricula.cartorioEstrangeiro }}</td><td md-cell>{{ matricula.numero }}</td><td md-cell ng-class=\"matricula.dataRegistro ? \'\' : \'md-cell-null\'\">{{ (matricula.dataRegistro | date:\'dd/MM/yyyy\') || \'-\' }}</td><td md-cell ng-show=!isDetail><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=confirmaRemocao(matricula) class=\"md-fab md-raised md-mini\" aria-label=\"Remover Matrícula\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Remover Matrícula</md-tooltip></md-icon></md-button><md-button ng-click=alteraMatricula(matricula) class=\"md-fab md-raised md-mini\" aria-label=\"Editar Matrícula\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Editar Matrícula</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacaoMatriculas.limit md-limit-options=paginacaoMatriculas.limitOptions md-page=paginacaoMatriculas.page md-total=\"{{ imovel.incorporacao.matriculas.length }}\" md-page-select ng-disabled=\"imovel.incorporacao.matriculas.length < 6\"></md-table-pagination></div></fieldset>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/proprietario/autarquia/view/dados-autarquia.html","<div layout=row layout-xs=column ng-controller=DadosAutarquiaController><md-input-container style=width:100%><div layout-gt-xs=row class=layout-gt-xs-row ng-if=!isDetail><md-input-container flex-gt-xs><label>Buscar por UG/CNPJ</label><input campo-ajuda=INC_FORM_DADOS_GERAIS_MATRIC_TRANSC type=text ng-model=inputUgCnpj ng-blur=validarUg(inputUgCnpj) ng-required=isInputUgCnpjObrigatorio() maxlength=18 class=secaoDadosIncorporacao name=inputUgCnpj></md-input-container></div><div layout-gt-xs=row class=tamanho-container ng-if=\"isDetail || mostraAutarquia\"><md-input-container class=\"md-block md-input-has-value\" flex=33><label>UG</label><div campo-ajuda=\"\"><div ng-if=!isLocatario class=\"ng-binding md-input\" disabled>{{proprietario.ugAutarquia != null? proprietario.ugAutarquia : \'-\'}}</div><div ng-if=isLocatario class=\"ng-binding md-input\" disabled>{{proprietario.ugLocatario != null? proprietario.ugLocatario : \'-\'}}</div></div></md-input-container><md-input-container class=\"md-block md-input-has-value\" flex=33><label>CNPJ</label><div campo-ajuda=\"\"><div ng-if=!isLocatario class=\"ng-binding md-input\" disabled>{{proprietario.cnpjAutarquia != null? proprietario.cnpjAutarquia : \'-\'}}</div><div ng-if=isLocatario class=\"ng-binding md-input\" disabled>{{proprietario.cnpjLocatario != null? proprietario.cnpjLocatario : \'-\'}}</div></div></md-input-container><md-input-container class=\"md-block md-input-has-value\" flex=33><label>Nome</label><div campo-ajuda=\"\"><div ng-if=!isLocatario class=\"ng-binding md-input\" disabled>{{proprietario.nomeAutarquia != null? proprietario.nomeAutarquia : \'-\'}}</div><div ng-if=isLocatario class=\"ng-binding md-input\" disabled>{{proprietario.nomeLocatario != null? proprietario.nomeLocatario : \'-\'}}</div></div></md-input-container></div></md-input-container></div>");
$templateCache.put("scripts-cadastro-imovel/app/directives/secoes-formulario/proprietario/fieldset-terceiros/view/fieldset-terceiros.html","<fieldset class=fieldset ng-if=\"isTipoProprietarioAnteriorTerceiros() || isImovelDeTerceiros() || isTipoProprietarioTerceiros() \"><legend><label style=\"color: rgb(76, 175, 80)\">Terceiros</label></legend><div layout-gt-xs=row ng-hide=terceiroNaoPossuiCpfOuCnpj()><!-- CPF/CNPJ --><md-input-container class=md-block flex-gt-sm flex><label>CPF/CNPJ</label><input campo-ajuda=INC_FORM_TERCEIROS_CNPJ_CPF ng-required=isInputCpfCnpjTerceirosObrigatorio() ng-disabled=\"isDetail || desabilitar\" cpf-cnpj-mask ng-model=ctrl.terceiroProprietario.numeroDocumento ng-blur=validaNumeroDocumento()></md-input-container><!-- Nome/Razão Social --><div flex-gt-sm flex style=\"margin-top: 5px; margin-left: 50px\" layout=column><label>Nome/Razão Social</label><label campo-ajuda=INC_FORM_TERCEIROS_NOME_RAZAO style=\"margin-top: 7px\">{{ctrl.terceiroProprietario.nomeProprietarioReceita}}</label></div></div><div layout-gt-xs=row ng-if=mostraOpcaoTerceiroNaoPossuiCpfOuCnpj()><md-input-container class=md-block flex-gt-sm flex><md-checkbox campo-ajuda=INC_FORM_TERCEIROS_NAO_POSSUI_CPF_CNPJ class=secaoDadosDescricaoCartorial ng-disabled=\"isDetail || desabilitar\" ng-model=proprietario.naoPossuiCpfOuCnpj>Não possui CPF/CNPJ</md-checkbox></md-input-container></div><md-input-container ng-show=terceiroNaoPossuiCpfOuCnpj() class=md-block flex-gt-xs class=md-block><label>Justificativa</label><textarea campo-ajuda=INC_FORM_TERCEIROS_JUSTIFICATIVA ng-required=terceiroNaoPossuiCpfOuCnpj() ng-disabled=\"isDetail || desabilitar\" ng-model=proprietario.justificativa md-maxlength=500 required></textarea></md-input-container><div layout=row layout-align=\"end center\" ng-hide=\"terceiroNaoPossuiCpfOuCnpj() || isDetail || desabilitar\"><md-button class=\"md-primary md-raised\" ng-click=adicionaTerceiro()>Adicionar</md-button></div><!-- Tabela Template --><div class=mgt-25 ng-hide=\"terceiroNaoPossuiCpfOuCnpj() || proprietario.pessoas.length === 0\"><md-content flex-xs=100 flex=100><md-table-container><table md-table><thead md-head md-order=ordenacao><tr md-row><th md-column md-order-by=numeroDocumento>CPF/CNPJ</th><th md-column md-order-by=nomeProprietarioReceita>Nome/Razão Social</th><th md-column ng-hide=isDetail>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"pessoa in proprietario.pessoas | orderBy: ordenacao | limitTo: paginacao.limit: (paginacao.page - 1) * paginacao.limit\"><td md-cell>{{ pessoa.numeroDocumento }}</td><td md-cell>{{ pessoa.nomeProprietarioReceita }}</td><td md-cell ng-hide=isDetail><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=removerTerceiro(pessoa) class=\"md-fab md-raised md-mini\" aria-label=\"Remover Proprietário\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Remover Proprietário</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=paginacao.limit md-limit-options=paginacao.limitOptions md-page=paginacao.page md-total=\"{{ proprietario.pessoas.length }}\" md-page-select ng-disabled=\"proprietario.pessoas.length < 6\"></md-table-pagination></md-content></div></fieldset>");}]);
(function () {
  'use strict';

  angular.module('su-acervo', [
    'su-componentes']);

})();

(function () {
  'use strict';

  service.$inject = ["$http"];
  function service($http) {

    var baseURL = '/acervo/api';

    var getArquivosAcervo = function (id) {
      return $http.get( baseURL + '/arquivo-acervo/' + id);
    };

    var alterarSituacaoPublicadoInternet = function (data) {
      return $http.post(baseURL + '/arquivo-acervo/alterarSituacaoPublicadoInternet', data);
    };

    var gerarEtiqueta = function (id) {
      return $http.post(baseURL + '/arquivo-acervo/etiqueta/' + id);
    };

    var deletarArquivoAcervo = function (idArquivoAcervo) {
      return $http.delete( baseURL + '/arquivo-acervo/' + idArquivoAcervo );
    };

    var listarArquivoAcervoParaGereciar = function (id){
      return $http.get( baseURL + '/arquivo-acervo/listar-hierarquia/' + id);
    };

    var alterarArquivoAcervoPrincipal = function (id){
      return $http.get( baseURL + '/arquivo-acervo/alterar-principal/' + id);
    };

    var autuarProtocoloSEI = function (data) {
      return $http.post(baseURL + '/integracao-sei', data);
    };

    return {
      getArquivosAcervo: getArquivosAcervo,
      alterarSituacaoPublicadoInternet: alterarSituacaoPublicadoInternet,
      gerarEtiqueta: gerarEtiqueta,
      deletarArquivoAcervo: deletarArquivoAcervo,
      listarArquivoAcervoParaGereciar: listarArquivoAcervoParaGereciar,
      alterarArquivoAcervoPrincipal: alterarArquivoAcervoPrincipal,
      autuarProtocoloSEI: autuarProtocoloSEI,
    }
  }

  angular.module('su-acervo').factory('EditarDocumentoService', service);

})();

(function () {
    'use strict';
    angular.module('su-acervo').controller('ModalVisualizarRelacionadosController',
      ['$timeout', '$scope','$rootScope', '$mdDialog', '$state', 'model', 'EditarDocumentoService', 'toastService',
        function ($timeout, $scope, $rootScope, $mdDialog, $state, model, EditarDocumentoService, toastService) {
  
          var vm = $scope;
          var isAlteracao = false;
          vm.listaAcervosAssociados = [];
  
          vm.fechar = fechar;
          vm.goVisualizarDocumento = goVisualizarDocumento;

  
          if(model.arquivoAcervo){
            vm.listaAcervosAssociados = model.arquivoAcervo.listaAcervosAssociadosDTO;
          }

          function goVisualizarDocumento(id) {
            $state.go('ACERVO.EDITAR-DOCUMENTO', {'idAcervo': id});
            $mdDialog.cancel();
          }
  
          function fechar() {
            $mdDialog.cancel();
          }
  
        }]);
  })();
  
(function () {
  'use strict';
  angular.module('su-acervo').controller('EditarDocumentoController',
    ['$scope', '$state', '$stateParams', '$mdDialog', 'EditarDocumentoService', 'toastService',
      function ($scope, $state, $stateParams, $mdDialog, EditarDocumentoService, toastService) {

        var vm = $scope;

        carregarListaArquivosAcervoMaisFilhos($stateParams.idAcervo);

        vm.editar = editar;
        vm.visualizar = visualizar;
        vm.novo = novo;
        vm.onChangePrincipal = onChangePrincipal;
        vm.alterarPublicacaoInternet = alterarPublicacaoInternet;
        vm.gerarEtiqueta = gerarEtiqueta;
        vm.mostrarInformacoesArquivoAnexo = mostrarInformacoesArquivoAnexo;
        vm.deletarArquivoAcervo = deletarArquivoAcervo;
        vm.possuiDerivadaPDF = possuiDerivadaPDF;
        vm.enviarParaSEI = enviarParaSEI;
        vm.cancelarEnvioSEI = cancelarEnvioSEI;
        vm.alterarTipoEnvioSEI = alterarTipoEnvioSEI;
        vm.isPossuiAcervosAssociados = isPossuiAcervosAssociados;
        vm.goVisualizarRelacionados = goVisualizarRelacionados;
        vm.isPossuiMaisAnexos = isPossuiMaisAnexos;
        vm.goVisualizarAnexos = goVisualizarAnexos;
        vm.isArquivoPodeIntegrarSei = isArquivoPodeIntegrarSei;

        function isArquivoPodeIntegrarSei(pArquivoAnexoDTO) {

          // Se não TEM TIFF e ainda não foi enviado.
          if (!pArquivoAnexoDTO.icDerivadaTiff
            && pArquivoAnexoDTO.protocoloIntegracaoSei == null) {
            return true;
          }

          // Se é TIFF e ainda não foi enviado e é um PDF
          if (pArquivoAnexoDTO.icDerivadaTiff && pArquivoAnexoDTO.protocoloIntegracaoSei == null
            && pArquivoAnexoDTO.dsTipoArquivoDTO != null
            && pArquivoAnexoDTO.dsTipoArquivoDTO.endsWith("application/pdf")) {
            return true;
          }

          return false;
        }

        function carregarListaArquivosAcervoMaisFilhos(idArquivoAcervo) {
          EditarDocumentoService.listarArquivoAcervoParaGereciar(idArquivoAcervo).then(function (response) {
            vm.listaAnexosAcervosMaisFilhos = [];
            vm.listaArquivosAcervosMaisFilhos = response.data;
            angular.forEach(vm.listaArquivosAcervosMaisFilhos, function (pAcervo, pKey) {
              vm.listaAnexosAcervosMaisFilhos = vm.listaAnexosAcervosMaisFilhos.concat(pAcervo.listArquivosAnexosDTO);
            });
            console.log(vm.listaAnexosAcervosMaisFilhos);
          }).catch(function () {
            toastService.mostarErro("Erro ao carregar lista de acervos.");
          });
        }

        function mostrarInformacoesArquivoAnexo(arquivoAcervoDTO) {
          if (arquivoAcervoDTO && arquivoAcervoDTO.listArquivosAnexosDTO && arquivoAcervoDTO.listArquivosAnexosDTO.length > 0) {
            if (!arquivoAcervoDTO.descritoresTecnicosDTO) {
              return {nome: "", tipo: "", formato: ""};
            }

            var arquivoAcervoPai = {};

            if (arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO) {
              for (var index in arquivoAcervoDTO.listArquivosAnexosDTO) {
                if (arquivoAcervoDTO.listArquivosAnexosDTO[index].dsTipoArquivoDTO.localeCompare("image/tiff") === 0) {
                  arquivoAcervoPai.nome = arquivoAcervoDTO.listArquivosAnexosDTO[index].dsArquivoAnexoDTO;
                  arquivoAcervoPai.tipo = arquivoAcervoDTO.listArquivosAnexosDTO[index].dsFormatoDTO;
                  arquivoAcervoPai.formato = arquivoAcervoDTO.listArquivosAnexosDTO[index].dsTipoArquivoDTO;
                }
              }
            } else {
              arquivoAcervoPai.nome = arquivoAcervoDTO.listArquivosAnexosDTO[0].dsArquivoAnexoDTO;
              arquivoAcervoPai.tipo = arquivoAcervoDTO.listArquivosAnexosDTO[0].dsFormatoDTO;

              if (arquivoAcervoDTO.listArquivosAnexosDTO.length >= 2) {
                arquivoAcervoPai.formato = arquivoAcervoDTO.listArquivosAnexosDTO[0].dsTipoArquivoDTO + ", " + arquivoAcervoDTO.listArquivosAnexosDTO[1].dsTipoArquivoDTO;
              } else {
                arquivoAcervoPai.formato = arquivoAcervoDTO.listArquivosAnexosDTO[0].dsTipoArquivoDTO
              }

            }

            return arquivoAcervoPai;
          }
        }

        function possuiDerivadaPDF(arquivoAcervoDTO) {
          for (var i = 0; i < arquivoAcervoDTO.listArquivosAnexosDTO.length; i++) {
            if (arquivoAcervoDTO.listArquivosAnexosDTO[i].dsTipoArquivoDTO.endsWith("/pdf")) {
              return true;
            }
          }
          return false;
        }

        function editar(idArquivoAcervo) {
          $state.go('ACERVO.DESCRITORES-INFORMACIONAIS', {'idAcervo': idArquivoAcervo});
        }

        function visualizar(idArquivoAcervo) {
          $state.go('ACERVO.DESCRITORES-INFORMACIONAIS-VISUALIZAR', {'idAcervo': idArquivoAcervo});
        }

        function onChangePrincipal(idArquivoAcervo) {
          EditarDocumentoService.alterarArquivoAcervoPrincipal(idArquivoAcervo).then(function (response) {
            toastService.mostarSucesso("Alteração realizada.");
            carregarListaArquivosAcervoMaisFilhos($stateParams.idAcervo);
          }).catch(function () {
            toastService.mostarErro("Erro ao alterar o documento principal.");
          });
        }

        function novo() {
          var idArquivoPai = null;
          for (var index in vm.listaArquivosAcervosMaisFilhos) {
            if (vm.listaArquivosAcervosMaisFilhos[index].icPrincipalDTO && vm.listaArquivosAcervosMaisFilhos[index].situacaoDTO.idSituacaoDTO === 1) {
              idArquivoPai = vm.listaArquivosAcervosMaisFilhos[index].idArquivoAcervoDTO;
              break;
            }
          }
          if (idArquivoPai == null) {
            toastService.mostarErro("Não é permitido vincular um acervo para este item.");
            return;
          }

          $state.go('ACERVO.DESCRITORES-INFORMACIONAIS', {'idAcervo': null, 'idAcervoPai': idArquivoPai});
        }

        function alterarPublicacaoInternet(arquivoAcervo) {
          EditarDocumentoService.alterarSituacaoPublicadoInternet(arquivoAcervo).then(function () {
            toastService.mostarSucesso("Alteração realizada.");
          }).catch(function (erro) {
            toastService.mostarErro("Erro ao executar alteração.");
          });
        }

        function gerarEtiqueta(idArquivoAcervo) {
          var a = document.createElement("a");
          a.style = "display: none";
          a.href = '/acervo/api/arquivo-acervo/etiqueta/' + idArquivoAcervo;
          a.download = "etiqueta.pdf";
          document.body.appendChild(a);
          a.click();
        }

        function deletarArquivoAcervo(idArquivoAcervo) {
          var confirm = $mdDialog.confirm()
            .title('Confirmação')
            .textContent('Tem certeza que deseja excluir este registro?')
            .ok('Confirmar')
            .cancel('Cancelar');

          $mdDialog.show(confirm).then(function () {
            EditarDocumentoService.deletarArquivoAcervo(idArquivoAcervo).then(function (response) {
              toastService.mostarSucesso("Registro de Exclusão Salvo com Sucesso!");
              if (response.data) {
                $state.go('ACERVO.EDITAR-DOCUMENTO', {'idAcervo': response.data});
                carregarListaArquivosAcervoMaisFilhos(response.data);
              } else {
                $state.go('ACERVO.CONSULTAR');
              }
            }).catch(function () {
              toastService.mostarErro('Erro ao deletar o arquivo acervo.');
            });
          }, function () {

          });
        }

        function enviarParaSEI() {
          var listaEnviar = vm.listaAnexosAcervosMaisFilhos.filter(function (item) {
            return item.icEnviarSEI == true
          });
          if (listaEnviar.length == 0) {
            toastService.mostarErro("Nenhum arquivo selecionado para enviar.");
            return;
          }

          var envios = [];
          for (var i = 0; i < listaEnviar.length; i++) {
            envios.push({
              "idArquivoAnexo": listaEnviar[i].idArquivoAnexoDTO,
              "protocoloSei": vm.numeroProcessoExistenteSei ? vm.numeroProcessoExistenteSei : vm.numeroProcessoAutuarSei,
              "autuacaoProcessoInformado": vm.tipoEnvioSEI == "3"
            });
          }

          if (vm.tipoEnvioSEI == "1") {
            EditarDocumentoService.autuarProtocoloSEI(envios).then(function () {
              toastService.mostarSucesso("Autuação do processo realizada.");
              carregarListaArquivosAcervoMaisFilhos($stateParams.idAcervo);
              cancelarEnvioSEI();
            }).catch(function (erro) {
              toastService.mostarErro("Erro ao executar autuação do processo.");
            });
          } else if (vm.tipoEnvioSEI == "2") {
            if (vm.numeroProcessoExistenteSei == null) {
              toastService.mostarErro("Número do processo não preenchido.");
              return;
            }

            EditarDocumentoService.autuarProtocoloSEI(envios).then(function () {
              toastService.mostarSucesso("Atualização do processo realizada.");
              carregarListaArquivosAcervoMaisFilhos($stateParams.idAcervo);
              cancelarEnvioSEI();
            }).catch(function (erro) {
              toastService.mostarErro("Erro ao executar atualização do processo.");
            });
          } else if (vm.tipoEnvioSEI == "3") {
            if (vm.numeroProcessoAutuarSei == null) {
              toastService.mostarErro("Número do processo não preenchido.");
              return;
            }

            EditarDocumentoService.autuarProtocoloSEI(envios).then(function () {
              toastService.mostarSucesso("Autuação do processo realizada.");
              carregarListaArquivosAcervoMaisFilhos($stateParams.idAcervo);
              cancelarEnvioSEI();
            }).catch(function (erro) {
              toastService.mostarErro("O processo informado já existe no SEI.");
            });
          } else {
            toastService.mostarErro("Tipo de envio para o SEI não selecionado.");
          }
        }

        function cancelarEnvioSEI() {
          for (var i = 0; i < vm.listaAnexosAcervosMaisFilhos.length; i++) {
            vm.listaAnexosAcervosMaisFilhos[i].icEnviarSEI = false;
            vm.tipoEnvioSEI = null;
            vm.numeroProcessoExistenteSei = null;
            vm.numeroProcessoAutuarSei = null;
          }
        }

        function alterarTipoEnvioSEI() {
          vm.numeroProcessoExistenteSei = null;
          vm.numeroProcessoAutuarSei = null;
        }

        function isPossuiAcervosAssociados(arquivoAcervo) {
          return (arquivoAcervo.listaAcervosAssociadosDTO.length > 0)
        }

        function goVisualizarRelacionados(arquivoAcervo) {
          $mdDialog.show({
            controller: 'ModalVisualizarRelacionadosController',
            templateUrl: 'scripts-acervo/app/editar-documento/view/modal-visualizar-acervos-relacionados.tmpl.html',
            model: {
              arquivoAcervo: arquivoAcervo
            },
            clickOutsideToClose: false,
            escapeToClose: false
          });
        }

        function isPossuiMaisAnexos(arquivoAcervo) {
          for (var index in arquivoAcervo.listArquivosAnexosDTO) {
            if (index < (arquivoAcervo.listArquivosAnexosDTO.length - 1) && arquivoAcervo.listArquivosAnexosDTO[index].coParteAnexoDTO != arquivoAcervo.listArquivosAnexosDTO[parseInt(index) + 1].coParteAnexoDTO) {
              return true;
            }
          }
          return false;
        }

        function goVisualizarAnexos(arquivoAcervo) {
          $state.go('ACERVO.ARQUIVO-ANEXO-VISUALIZAR', {'idAcervo': arquivoAcervo.idArquivoAcervoDTO});
        }

      }]);
})();

(function () {
  'use strict';

  function toast($mdToast) {

    var mostarErro = function (msg) {
      $mdToast.show(
        $mdToast.simple()
          .textContent(msg)
          .position('top right')
          .hideDelay(3000)
          .theme('error-toast')
      );
    };

    var mostarSucesso = function (msg) {
      $mdToast.show(
        $mdToast.simple()
          .textContent(msg)
          .position('top right')
          .hideDelay(3000)
          .theme('success-toast')
      );
    };

    var mostrarProtocoloAcervo = function (msg) {
      $mdToast.show(
        $mdToast.simple()
          .textContent(msg)
          .position('top right')
          .hideDelay(30000)
          .theme('success-toast')
      );
    };

    return {
      mostarErro: mostarErro,
      mostarSucesso: mostarSucesso,
      mostrarProtocoloAcervo: mostrarProtocoloAcervo
    };
  }

  angular.module('su-acervo').factory('toastService', ['$mdToast', toast]);

})();

(function () {
  'use strict';
  angular.module('su-acervo')
    .directive('partialDateField', function () {
      return {
        restrict: 'E',
        templateUrl: 'scripts-acervo/app/custom-components/partial-date-field/partial-date-field.tmpl.html',
        scope: {
          model: '=',
          label: '=',
          flex: '=',
          obrigatorio: '=',
          desabilitado: '=',
          id: '='
        },
        controller: ["$scope", "$rootScope", function ($scope, $rootScope) {

          $rootScope.$on('limparDiretiva', limparDiretiva);

          $scope.dia = null;
          $scope.mes = null;
          $scope.ano = null;

          if($scope.model) {
            var data = $scope.model.split('/');
            $scope.dia = Number(data[0]);
            $scope.mes = Number(data[1]);
            $scope.ano = data[2];
          }

          function limparDiretiva() {
            $scope.dia = null;
            $scope.mes = null;
            $scope.ano = null;
          }

          $scope.onChange = function () {
            $scope.model = ($scope.dia === null ? "" : $scope.dia) + '/' +
              ($scope.mes === null ? "" : $scope.mes) + '/' +
              ($scope.ano === null ? "" : $scope.ano);
          }

          $scope.isAceitaNumero = function () {
            return ($scope.id == "processoAtuacaoData" || $scope.id == "originalData" ) ;
          }
        }]
      }
    });
})();

(function () {
  'use strict';
  angular.module('su-acervo')
    .directive('numbersOnly', [function () {
      return {
        require: 'ngModel',
        link: function (scope, element, attr, ngModelCtrl) {
          function fromUser(text) {
            if (text) {
              var transformedInput = text.replace(/[^0-9]/g, '');

              if (transformedInput !== text) {
                ngModelCtrl.$setViewValue(transformedInput);
                ngModelCtrl.$render();
              }
              return transformedInput;
            }
            return undefined;
          }
          ngModelCtrl.$parsers.push(fromUser);
        }
      };
    }])
})();

(function () {
  'use strict';
  angular.module('su-acervo')
    .directive('multiCompositeField', function () {
      return {
        restrict: 'E',
        templateUrl: 'scripts-acervo/app/custom-components/multi-composite-field/multi-composite-field.tmpl.html',
        scope: {
          model: '=',
          label: '=',
          name: '=',
          flex: '=',
          disabled: '=',
          obrigatorio: '='
        },
        controller: ["$scope", function ($scope) {

          if (!$scope.model || $scope.model.length === 0) {
            $scope.model = [];
            var object = {};
            object[$scope.name] = null;
            $scope.model.push(object);
          }

          $scope.add = function () {
            var object = {};
            object[$scope.name] = null;
            $scope.model.push(object);
          };

          $scope.remove = function (index) {
            $scope.model.splice(index, 1);
          };
        }]
      }
    });
})();

(function () {
  'use strict';
  angular.module('su-acervo').config(['cfpLoadingBarProvider', function(cfpLoadingBarProvider) {
    cfpLoadingBarProvider.spinnerTemplate = '<div class="panelLoading-background"></div>' +
      '<div id="modalLoading" class="modal-loading" tabindex="-1" aria-hidden="true" data-backdrop="static" data-keyboard="false">' +
      '	<div class="modal-loading-body">' +
      '		<div class="sk-circle">' +
      '		  <div class="sk-circle1 sk-child"></div>' +
      '		  <div class="sk-circle2 sk-child"></div>' +
      '		  <div class="sk-circle3 sk-child"></div>' +
      '		  <div class="sk-circle4 sk-child"></div>' +
      '		  <div class="sk-circle5 sk-child"></div>' +
      '		  <div class="sk-circle6 sk-child"></div>' +
      '		  <div class="sk-circle7 sk-child"></div>' +
      '		  <div class="sk-circle8 sk-child"></div>' +
      '		  <div class="sk-circle9 sk-child"></div>' +
      '		  <div class="sk-circle10 sk-child"></div>' +
      '		  <div class="sk-circle11 sk-child"></div>' +
      '		  <div class="sk-circle12 sk-child"></div>' +
      '		</div>' +
      '	</div>' +
      '<p>Carregando...</p>' +
      '</div>';
  }]);
})();

(function () {
  'use strict';
  angular.module('su-acervo')
    .directive('fileModel', ["$parse", function ($parse) {
      return {
        restrict: 'A',

        link: function (scope, element, attrs) {
          var model = $parse(attrs.fileModel);
          var modelSetter = model.assign;

          element.bind('change', function(){
            scope.$apply(function(){
              modelSetter(scope, element[0].files[0]);
            });
          });
        }
      }
    }]);
})();

(function () {
  'use strict';
  angular.module('su-acervo')
    .directive('botoesCadastro', function () {
      return {
        restrict: 'E',
        templateUrl: 'scripts-acervo/app/custom-components/botoes-cadastro/botoes-cadastro.tmpl.html',
        scope: {
          previous: '&',
          cancelar: '&',
          save: '&',
          next: '&',
          showPrevious: '&',
          showNext: '&',
          showSave: '&'
        }
      }
    });
})();

(function () {
  'use strict';

  service.$inject = ["$http"];
  function service($http) {

    var baseURL = '/acervo/api';

    var postConsulta = function (data) {
      return $http.post(baseURL + '/arquivo-acervo/consultar', data);
    };

    var postConsultaPagina = function (data) {
      return $http.post(baseURL + '/arquivo-acervo/consultar/pagina', data);
    };

    return {
      postConsulta: postConsulta,
      postConsultaPagina: postConsultaPagina
    }
  }

  angular.module('su-acervo').factory('ConsultaService', service);

})();

(function () {
  'use strict';
  var acervoModule = angular.module('su-acervo');

  acervoModule.filter('cincoPalavrasChave', function() {
    return function(input) {
      var index = input.length < 5 ? input.length : 5;
      var words = [];
      for (var i = 0; i < index; i++) {
        words[i] = input[i].dsPalavraChaveDTO;
      }
      if(words.length == 0) {
        return "";
      } else {
        return words.join(', ') + ".";
      }
    };
  });

  acervoModule.filter('numeroProtocoloAcervo', function() {
    return function(input) {
      return input.substring(0, 4) + '.' + input.substring(4, 5) + '.' + input.substring(5, 6) + '.' + input.substring(6, input.length - 2) + "-" + input.substr(input.length - 2);
    };
  });

  acervoModule.controller('ConsultarController',
    ['$scope', '$state', 'CadastroDocumentoService', 'ConsultaService', 'toastService', '$mdDialog',
      function ($scope, $state, CadastroDocumentoService, ConsultaService, toastService, $mdDialog) {

        var vm = $scope;

        vm.filterMapping = [];
        vm.arquivoAcertoRestricoes = {};
        vm.protocoloAcertoRestricoes = {};
        vm.resultados = [];
        vm.tiposOrdenacao = [
          { descricao: "Mais recente", valor: "MAIS_RECENTE"},
          { descricao: "Mais antigo", valor: "MAIS_ANTIGO"},
          { descricao: "Tipo (A-Z)", valor: "TIPO"}
        ];

        CadastroDocumentoService.getCategorias().then(function (retorno) {
          vm.categorias = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar Categorias');
        });

        CadastroDocumentoService.getEspecieDocumentais().then(function (retorno) {
          vm.especieDocumentais = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar Especies documentais');
        });

        vm.pesquisar = pesquisar;
        vm.limparPesquisa = limparPesquisa;
        vm.goCadastrarDocumento = goCadastrarDocumento;
        vm.goAlterarDocumento = goAlterarDocumento;
        vm.goEditarDocumento = goEditarDocumento;
        vm.goVisualizarDocumento = goVisualizarDocumento;
        vm.addFiltro = addFiltro;
        vm.removerFiltro = removerFiltro;
        vm.onChangeCategoria = onChangeCategoria;
        vm.onChangeEspecieDocumental = onChangeEspecieDocumental;

        function onChangeCategoria() {
          vm.arquivoAcertoRestricoes.especieDocumental = null;
          vm.arquivoAcertoRestricoes.descritorInformacional = null;
          vm.arquivoAcertoRestricoes.descritorCatalografico = null;

          if(vm.arquivoAcertoRestricoes.categoria == 2) { // Iconográfico
            carregarDescritoresInformacionaisIconografico();
            carregarDescritoresCatalograficosIconografico();
          }
        }

        function onChangeEspecieDocumental() {
          if(vm.arquivoAcertoRestricoes.especieDocumental == 1) {
            carregarDescritoresInformacionaisTextualProcessos();
          } else {
            carregarDescritoresInformacionaisTextualFichasFinanceiras();
          }
          carregarDescritoresCatalograficosTextual();
        }

        function carregarDescritoresInformacionaisTextualProcessos() {
          vm.descritoresInformacionais = [];
          vm.descritoresInformacionais.push({ descricao: "Processo Administrativo", valor: "descritoesInformacionais.dsProcessoAdmnistrativo", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Assunto", valor: "descritoesInformacionais.assunto.dsAssunto", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Título Formal", valor: "descritoesInformacionais.dsTituloFormal", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Órgão Emissor", valor: "descritoesInformacionais.dsOrgaoEmissor", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Interessados", valor: "descritoesInformacionais.listInteressados.dsInteressados", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Logradouro Original", valor: "descritoesInformacionais.dsLogradouroOriginal", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Confrontações", valor: "descritoesInformacionais.dsConfrontacoes", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Logradouro Atualizado", valor: "descritoesInformacionais.dsLogradouroAtualizado", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Unidade Federativa", valor: "descritoesInformacionais.unidadeFederativa.dsUnidadeFederativa", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Tipo de Imóvel", valor: "descritoesInformacionais.patrimonio.dsPatrimonio", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Denominação Patrimonial", valor: "descritoesInformacionais.dsDenominacaoPatrimonial", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Data de Autuação do Processo", valor: "descritoesInformacionais.dtAtuacaoProcesso", texto: false });
          vm.descritoresInformacionais.push({ descricao: "Pasta de Registro de Referência", valor: "descritoesInformacionais.dsPastaRequisitoRef", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Número SEI", valor: "descritoesInformacionais.nuSei", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Processos Antecedentes", valor: "descritoesInformacionais.listProcessoAntecedente.dsProcessoAntecedente", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Processo de Outro Órgão", valor: "descritoesInformacionais.dsProcessoOutroOrgao", texto: true });
          vm.descritoresInformacionais.push({ descricao: "RIP", valor: "descritoesInformacionais.nuRip", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Informações Complementares", valor: "descritoesInformacionais.dsInfoComplementares", texto: true });
        }

        function carregarDescritoresInformacionaisTextualFichasFinanceiras() {
          vm.descritoresInformacionais = [];
          vm.descritoresInformacionais.push({ descricao: "RIP SPIU", valor: "descritoesInformacionais.nuRipSpiu", texto: true });
          vm.descritoresInformacionais.push({ descricao: "RIP SIAPA", valor: "descritoesInformacionais.nuRipSiapa", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Logradouro Original", valor: "descritoesInformacionais.dsLogradouroOriginal", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Logradouro Atualizado", valor: "descritoesInformacionais.dsLogradouroAtualizado", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Informações Complementares", valor: "descritoesInformacionais.dsInfoComplementares", texto: true });
        }

        function carregarDescritoresCatalograficosTextual() {
          vm.descritoresCatalograficos = [];
          vm.descritoresCatalograficos.push({ descricao: "Estante", valor: "descritorCatalografico.dsEstante", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Prateleira", valor: "descritorCatalografico.dsPrateleira", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Caixa", valor: "descritorCatalografico.dsCaixa", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Kardex?", valor: "descritorCatalografico.icNotacaoKardex", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Armário", valor: "descritorCatalografico.listNotacaoKardex.dsArmario", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Gaveta", valor: "descritorCatalografico.listNotacaoKardex.dsGaveta", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Maço", valor: "descritorCatalografico.listNotacaoKardex.dsMaco", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Dimensão do Suporte", valor: "descritorCatalografico.dimensaoSuporte.dsDimensaoSuporte", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Condições do Original", valor: "descritorCatalografico.condicaoOriginal.dsCondicaoOriginal", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Iconografia", valor: "descritorCatalografico.icIconografia", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Fotografia ", valor: "descritorCatalografico.icEspecieFotografia", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Cartográfico-Arquitetônico", valor: "descritorCatalografico.icEspecieCartografia", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Informações Complementares", valor: "descritorCatalografico.txInformacoesComplementares", texto: true });
        }

        function carregarDescritoresInformacionaisIconografico() {
          vm.descritoresInformacionais = [];
          vm.descritoresInformacionais.push({ descricao: "Título do Documento", valor: "descritoesInformacionaisIconografico.dsTipoDocumento", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Unidade Federativa", valor: "descritoesInformacionaisIconografico.unidadeFederativa.dsUnidadeFederativa", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Município", valor: "descritoesInformacionaisIconografico.municipio.dsMunicipio", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Região Administrativa", valor: "descritoesInformacionaisIconografico.dsRegiaoAdministrativa", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Bairro", valor: "descritoesInformacionaisIconografico.dsBairro", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Logradouro Original", valor: "descritoesInformacionaisIconografico.dsLogradouroOriginalIconografico", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Confrontação", valor: "descritoesInformacionaisIconografico.dsConfrontacoesIconografico", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Logradouro Atualizado", valor: "descritoesInformacionaisIconografico.dsLogradouroAtualizadoIconografico", texto: true });
          vm.descritoresInformacionais.push({ descricao: "RIP", valor: "descritoesInformacionaisIconografico.nuRip", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Tipo Documental", valor: "descritoesInformacionaisIconografico.tipoDocumental.dsTipoDocumental", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Vinculado a Processos SPU", valor: "descritoesInformacionaisIconografico.icVinculadoSpu", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Processo Vinculado", valor: "descritoesInformacionaisIconografico.listProcessoVinculado.dsProcessoVinculado", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Órgão Emissor", valor: "descritoesInformacionaisIconografico.dsOrgaoEmissor", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Autor do Original", valor: "descritoesInformacionaisIconografico.listAutorOriginal.dsAutorOriginal", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Data do Original", valor: "descritoesInformacionaisIconografico.dtOriginal", texto: false });
          vm.descritoresInformacionais.push({ descricao: "Autor da Cópia", valor: "descritoesInformacionaisIconografico.listAutorCopia.dsAutorCopia", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Data da Cópia", valor: "descritoesInformacionaisIconografico.dtCopia", texto: false });
          vm.descritoresInformacionais.push({ descricao: "Escala Cartográfica", valor: "descritoesInformacionaisIconografico.nuEscalaCartografica", texto: false });
          vm.descritoresInformacionais.push({ descricao: "Área Total", valor: "descritoesInformacionaisIconografico.nuAreaTotal", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Unidade de Medida", valor: "descritoesInformacionaisIconografico.unidadeMedida.dsUnidadeMedida", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Área Equivalente", valor: "descritoesInformacionaisIconografico.nuAreaEquivalente", texto: false });
          vm.descritoresInformacionais.push({ descricao: "Agrupamento Temático", valor: "descritoesInformacionaisIconografico.agrupamentoTematico.dsAgrupamentoTematico", texto: true });
          vm.descritoresInformacionais.push({ descricao: "Informações Complementares", valor: "descritoesInformacionaisIconografico.dsInformacoesComplementares", texto: true });
        }

        function carregarDescritoresCatalograficosIconografico() {
          vm.descritoresCatalograficos = [];
          vm.descritoresCatalograficos.push({ descricao: "Item", valor: "descritorCatalograficoIconografico.dsItem", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Mapoteca", valor: "descritorCatalograficoIconografico.dsMapoteca", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Gaveta", valor: "descritorCatalograficoIconografico.dsGaveta", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Prancha", valor: "descritorCatalograficoIconografico.dsPrancha", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Planta", valor: "descritorCatalograficoIconografico.dsPlanta", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Armário", valor: "descritorCatalograficoIconografico.dsArmario", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Tela", valor: "descritorCatalograficoIconografico.dsTela", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Tubo", valor: "descritorCatalograficoIconografico.dsTubo", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Documento", valor: "descritorCatalograficoIconografico.documento.dsDocumento", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Órgão de Origem", valor: "descritorCatalograficoIconografico.dsOrgaoOrigem", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Órgão de Tutela", valor: "descritorCatalograficoIconografico.dsOrgaoTutela", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Material de Suporte", valor: "descritorCatalograficoIconografico.materialSuporte.dsMaterialSuporte", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Dimensão do Suporte", valor: "descritorCatalograficoIconografico.dimensaoSuporte.dsDimensaoSuporte", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Condições do Original", valor: "descritorCatalograficoIconografico.condicaoOriginal.dsCondicaoOriginal", texto: true });
          vm.descritoresCatalograficos.push({ descricao: "Informações Complementares", valor: "descritorCatalograficoIconografico.dsInfoComplementares", texto: true });
        }

        function validarFiltro() {
          if(vm.arquivoAcertoRestricoes.categoria == null
            || (vm.arquivoAcertoRestricoes.categoria == 1 && vm.arquivoAcertoRestricoes.especieDocumental == null)
            || (vm.arquivoAcertoRestricoes.descritorInformacional == null && vm.arquivoAcertoRestricoes.descritorCatalografico == null)) {
            return false;
          }

          var descritor = null;
          if(vm.arquivoAcertoRestricoes.descritorInformacional != null) {
            descritor = vm.arquivoAcertoRestricoes.descritorInformacional;
          } else {
            descritor = vm.arquivoAcertoRestricoes.descritorCatalografico;
            if(vm.arquivoAcertoRestricoes.descritorCatalografico.valor == "descritorCatalografico.icEspecieFotografia"
            || vm.arquivoAcertoRestricoes.descritorCatalografico.valor == "descritorCatalografico.icEspecieCartografia"){
              vm.arquivoAcertoRestricoes.texto = true;
            }
          }
          if((descritor.texto && vm.arquivoAcertoRestricoes.texto == null)
              || (!descritor.texto && (vm.arquivoAcertoRestricoes.minimo == null || vm.arquivoAcertoRestricoes.maximo == null))) {
            return false;
          }

          return true;
        }

        function addFiltro() {
          if(!validarFiltro()) {
            toastService.mostarErro('Campos não preenchidos corretamente!');
            return;
          }

          var campo = null;
          var descricao = null;
          if(vm.arquivoAcertoRestricoes.descritorInformacional != null) {
            campo = vm.arquivoAcertoRestricoes.descritorInformacional.valor;
            descricao = vm.arquivoAcertoRestricoes.descritorInformacional.descricao;
          } else if(vm.arquivoAcertoRestricoes.descritorCatalografico != null) {
            campo = vm.arquivoAcertoRestricoes.descritorCatalografico.valor;
            descricao = vm.arquivoAcertoRestricoes.descritorCatalografico.descricao;
          }

          var id = 1
          if (vm.filterMapping.length > 0) {
            id = vm.filterMapping[vm.filterMapping.length - 1].id + 1;
          }

          // Adiciona o filtro do campo
          vm.filterMapping.push({
            id: id,
            categoria: vm.arquivoAcertoRestricoes.categoria,
            especieDocumental: vm.arquivoAcertoRestricoes.especieDocumental,
            campo: campo,
            descricao: descricao,
            texto: vm.arquivoAcertoRestricoes.texto,
            minimo: vm.arquivoAcertoRestricoes.minimo,
            maximo: vm.arquivoAcertoRestricoes.maximo
          });

          limparFiltros();
        }

        function removerFiltro(id) {
          vm.filterMapping = vm.filterMapping.filter(function(item) { return item.id != id });
        }

        function pesquisar() {
          var filtro = {
            filterMapping: angular.copy(vm.filterMapping),
            tipoOrdenacao: vm.arquivoAcertoRestricoes.ordenacao
          };

          // Adiciona o filtro do campo Protocolo Acervo
          if (vm.protocoloAcertoRestricoes.texto != null) {
            var id = 1
            if (vm.filterMapping.length > 0) {
              id = vm.filterMapping[vm.filterMapping.length - 1].id + 1;
            }
            filtro.filterMapping.push({
              id: id,
              categoria: null,
              especieDocumental: null,
              campo: "coProtocoloAcervo",
              descricao: "Protocolo Acervo",
              texto: vm.protocoloAcertoRestricoes.texto.replace(/[^0-9]/g, ""),
              minimo: null,
              maximo: null,
              numeroPagina: null
            });
          }

          var confirm = $mdDialog.confirm()
            .title('ALERTA!')
            .textContent('A pesquisa será realizada sem nenhum filtro, confirma?')
            .ok('Confirmar')
            .cancel('Cancelar');

          if (filtro.filterMapping.length > 0) {
            filtrar(filtro);
          } else {
            $mdDialog.show(confirm).then(function () {
              vm.pesquisarPagina(0);
            }, function () {
            });
          }
        }

        vm.paginaAnterior = function (){
          if(vm.resultados[0].paginaAtualConsulta){
            if(vm.resultados[0].paginaAtualConsulta > 0) {
              vm.pesquisarPagina(vm.resultados[0].paginaAtualConsulta - 1);
            }else{
              vm.pesquisarPagina(0);
            }
          }
        };

        vm.proximaPagina = function (){
          if(vm.resultados[0].paginaAtualConsulta < vm.resultados[0].totalPaginasConsulta){
            vm.pesquisarPagina(vm.resultados[0].paginaAtualConsulta +1);
          }else{
            vm.pesquisarPagina(vm.resultados[0].totalPaginasConsulta);
          }
        };

        vm.pesquisarPaginaCombo = function () {
          vm.pesquisarPagina(vm.resultados[0].paginaAtualConsulta);
        };

        vm.pesquisarPagina = function (numero) {
          ConsultaService.postConsultaPagina(numero).then(function (retorno) {
            vm.resultados = retorno.data;
          }).catch(function (erro) {
            toastService.mostarErro("Erro ao executar a consulta.");
          });
        };

        function filtrar(filtro) {
          ConsultaService.postConsulta(filtro).then(function (retorno) {
            vm.resultados = retorno.data;
          }).catch(function (erro) {
            toastService.mostarErro("Erro ao executar a consulta.");
          });
        }

        function goCadastrarDocumento() {
          $state.go('ACERVO.DESCRITORES-INFORMACIONAIS', {'idAcervo': null});
        }

        function goAlterarDocumento(id) {
          $state.go('ACERVO.DESCRITORES-INFORMACIONAIS', {'idAcervo': id});
        }

        function goVisualizarDocumento(id) {
          $state.go('ACERVO.DESCRITORES-INFORMACIONAIS-VISUALIZAR', {'idAcervo': id});
        }

        function goEditarDocumento(id) {
          $state.go('ACERVO.EDITAR-DOCUMENTO', {'idAcervo': id});
        }

        function limparFiltros() {
          vm.arquivoAcertoRestricoes = {};
          vm.descritoresInformacionais = [];
          vm.descritoresCatalograficos = [];
        }

        function limparPesquisa() {
          limparFiltros();
          vm.filterMapping = [];
          vm.resultados = [];
          vm.protocoloAcertoRestricoes = {};
        }

      }]);
})();

(function () {
  'use strict';

  service.$inject = ["$http", "Upload"];
  function service($http, Upload) {

    var baseURL = '/acervo/api';

    var getCategorias = function () {
      return $http.get(baseURL + '/categoria');
    };

    var getEspecieDocumentais = function () {
      return $http.get(baseURL + '/especie-documental');
    };

    var getAssunto = function () {
      return $http.get(baseURL + '/assunto');
    };

    var getUnidadeFederativa = function () {
      return $http.get(baseURL + '/unidade-federativa');
    };

    var getPatrimonio = function () {
      return $http.get(baseURL + '/patrimonio');
    };

    var getEquipamentoUtilizado = function () {
      return $http.get(baseURL + '/equipamento-utilizado');
    };

    var getDimensaoImgRelacao = function () {
      return $http.get(baseURL + '/dimensao-img-relacao');
    };

    var getModoCores = function () {
      return $http.get(baseURL + '/modo-cores');
    };

    var getResolucaoLinear = function () {
      return $http.get(baseURL + '/resolucao-linear');
    };

    var getResolucaoTonal = function () {
      return $http.get(baseURL + '/resolucao-tonal');
    };

    var save = function (data) {
      return $http.post(baseURL + '/arquivo-acervo', data);
    };

    var getOneArquivoAcervo = function (idArquivoAcervo) {
      return $http.get( baseURL + '/arquivo-acervo/' + idArquivoAcervo);
    };

    var getDimensoesSuportePorCategoria = function (idCategoria) {
      return $http.get( baseURL + '/dimensao-suporte/' + idCategoria);
    };

    var getCondicoesOriginalPorCategoria = function (idCategoria) {
      return $http.get( baseURL + '/condicao-original/' + idCategoria);
    };

    var getEspecies = function () {
      return $http.get( baseURL + '/especie');
    };

    var salvarArquivo = function (arquivo) {
      return Upload.upload({
        url: baseURL + '/arquivo-anexo',
        data: arquivo,
        ignoreLoadingBar: true
      });
    };

    var alterarArquivo = function(arquivo){
      return $http.put(baseURL + '/arquivo-anexo', arquivo, {
        transformRequest: angular.identiry,
        headers: {'Content-Type': undefined}
      });
    };

    var verificarConversao = function(itemUpload){
      return $http.get(baseURL + '/arquivo-anexo/convertido/' + itemUpload.idArquivoAcervo + '/' + itemUpload.coParteAnexo, {ignoreLoadingBar: true});
    };

    var downloadArquivoAnexo = function (id) {
      return $http.get( baseURL + '/arquivo-anexo/' + id);
    };

    var deletarArquivoAnexo = function (id){
      return $http.delete(baseURL + '/arquivo-anexo/' + id)
    };

    var buscarMunicipioPorUnidadeFederativa = function (idUnidadeFederativa){
      return $http.get( baseURL + '/municipio/' + idUnidadeFederativa);
    };

    var getTipoDocumental = function () {
      return $http.get( baseURL + '/tipo-documental');
    };

    var getUnidadeMedida = function () {
      return $http.get( baseURL + '/unidade-medida');
    };

    var getAgrupamentoTematico = function () {
      return $http.get( baseURL + '/agrupamento-tematico');
    };

    var getDocumento = function () {
      return $http.get( baseURL + '/documento');
    };

    var getMaterialSuporte = function () {
      return $http.get( baseURL + '/material-suporte');
    };

	var getDescricaoHistorico = function(idHistoricoArquivoAcervo) {
      return $http.get( baseURL + '/historico-arquivo-acervo/descricao-historico/' + idHistoricoArquivoAcervo);
    };

    var buscarCep = function (cep) {
      return $http.get(baseURL + '/servicos/api/public/comum/localidade/cep/'+ cep);
  };

  var getUsuarioLogado = function() {
    return $http.get( baseURL + '/arquivo-acervo/usuario-logado');
  };

    return {
      getCategorias: getCategorias,
      getEspecieDocumentais: getEspecieDocumentais,
      getAssunto: getAssunto,
      getUnidadeFederativa: getUnidadeFederativa,
      getPatrimonio: getPatrimonio,
      getEquipamentoUtilizado: getEquipamentoUtilizado,
      getDimensaoImgRelacao: getDimensaoImgRelacao,
      getModoCores: getModoCores,
      getResolucaoLinear: getResolucaoLinear,
      getResolucaoTonal: getResolucaoTonal,
      save: save,
      getOneArquivoAcervo: getOneArquivoAcervo,
      getDimensoesSuportePorCategoria: getDimensoesSuportePorCategoria,
      getCondicoesOriginalPorCategoria: getCondicoesOriginalPorCategoria,
      getEspecies: getEspecies,
      salvarArquivo: salvarArquivo,
      verificarConversao: verificarConversao,
      alterarArquivo: alterarArquivo,
      downloadArquivoAnexo: downloadArquivoAnexo,
      deletarArquivoAnexo: deletarArquivoAnexo,
      buscarMunicipioPorUnidadeFederativa: buscarMunicipioPorUnidadeFederativa,
      getTipoDocumental: getTipoDocumental,
      getUnidadeMedida: getUnidadeMedida,
      getAgrupamentoTematico: getAgrupamentoTematico,
      getDocumento: getDocumento,
      getMaterialSuporte: getMaterialSuporte,
      getDescricaoHistorico: getDescricaoHistorico,
      buscarCep : buscarCep,
      getUsuarioLogado : getUsuarioLogado
    };
  }

  angular.module('su-acervo').factory('CadastroDocumentoService', service);

})();

(function () {
  'use strict';
  angular.module('su-acervo').controller('ModalNovoArquivoAnexoController',
    ['$timeout', '$scope','$rootScope', '$mdDialog', '$state', 'model', 'CadastroDocumentoService', 'toastService',
      function ($timeout, $scope, $rootScope, $mdDialog, $state, model, CadastroDocumentoService, toastService) {

        var vm = $scope;
        var isAlteracao = false;
        vm.arquivoAnexoDTO = {};

        vm.fechar = fechar;
        vm.anexar = anexar;
        vm.isAlteracaoArquivo = isAlteracaoArquivo;
        vm.alterarAnexo = alterarAnexo;

        if(model.isFichaFinanceira){
          vm.arquivoAnexoDTO.dsFormatoDTO = "Ficha";
          vm.arquivoAnexoDTO.dsArquivoAnexoDTO = "Financeira";
        }

        if(model.arquivoAnexo.idArquivoAnexo){
          isAlteracao = true;
          vm.arquivoAnexoDTO.idArquivoAnexoDTO = model.arquivoAnexo.idArquivoAnexo;
          vm.arquivoAnexoDTO.dsFormatoDTO = model.arquivoAnexo.dsTipo;
          vm.arquivoAnexoDTO.dsArquivoAnexoDTO = model.arquivoAnexo.dsNome;
          vm.arquivoAnexoDTO.dsArquivoGravadoDTO = model.arquivoAnexo.dsArquivoAnexo;
        }

        function alterarAnexo(){
          isAlteracao = false;
        }

        function isAlteracaoArquivo(){
          return isAlteracao;
        }

        function fechar() {
          $mdDialog.cancel();
        }

        function verificarFormatoArquivoValido(arquivo) {

          if(!arquivo){
            return true;
          }

          var formatosValiddos = ['application/pdf', 'image/jpeg', 'image/tiff'];

          if(formatosValiddos.indexOf(arquivo.type) < 0){
            toastService.mostarErro('Só é permitido anexar arquivos do tipo .PDF, .JPEG e .TIFF');
            return false;
          }

          if(arquivo.type != model.arquivoAnexo.formatoSelecionado){
            toastService.mostarErro('Deve ser inserido um arquivo do tipo ' + model.arquivoAnexo.formatoSelecionado);
            return false;
          }

          return true;
        }

        function enfileirarUpload() {
          if (!$rootScope.uploads) {
            $rootScope.uploads = [];
          }
          var itemUpload = {};
          itemUpload.nomeArquivo = vm.arquivoAnexoDTO.dsArquivoAnexoDTO;
          itemUpload.id = new Date().getTime();
          itemUpload.idArquivoAcervo = model.idArquivoAcervo;
          itemUpload.coParteAnexo = model.indice;
          itemUpload.upload = true;
          $rootScope.uploads.push(itemUpload);
          return itemUpload;
        }

        function removerEnfileiramento(idRemover) {
          $rootScope.uploads = jQuery.grep($rootScope.uploads, function (itemUpload, index) {
            return itemUpload.id != idRemover;
          });
        }

        function validarConversao(itemUpload) {
          $timeout(function () {
            CadastroDocumentoService.verificarConversao(itemUpload).then(function (retorno) {
              removerEnfileiramento(itemUpload.id);
              toastService.mostarSucesso('Conversão do arquivo "' + itemUpload.nomeArquivo + '" Realizado com Sucesso!');
              model.callback();
              itemUpload.remover = true;
            }).catch(function () {
              return false;
            });
          }, 10000).then(function () {
            if (!itemUpload.remover) {
              validarConversao(itemUpload);
            } else {
              removerEnfileiramento(itemUpload.id);
            }
          });
        }

        function anexar(form) {
          if (form.$valid) {
            if (vm.arquivoSelecionado) {

              var formData;
              if (verificarFormatoArquivoValido(vm.arquivoSelecionado)) {
                formData = {
                  'file': vm.arquivoSelecionado,
                  'dsFormato': vm.arquivoAnexoDTO.dsFormatoDTO,
                  'dsArquivoAnexo': vm.arquivoAnexoDTO.dsArquivoAnexoDTO,
                  'dsTipoArquivo': vm.arquivoSelecionado.type,
                  'idArquivoAcervo': model.idArquivoAcervo,
                  'idArquivoAnexo': (vm.arquivoAnexoDTO.idArquivoAnexoDTO ? vm.arquivoAnexoDTO.idArquivoAnexoDTO : ""),
                  'coParteAnexo': model.indice
                };

                var itemUpload = enfileirarUpload();

                CadastroDocumentoService.salvarArquivo(formData).then(function (retorno) {
                  toastService.mostarSucesso('Upload do arquivo "' + itemUpload.nomeArquivo + '" Realizado com Sucesso!');
                  model.callback();
                  if (formData.dsTipoArquivo === "image/tiff") {
                    itemUpload.upload = false;
                    validarConversao(itemUpload);
                  } else {
                    removerEnfileiramento(itemUpload.id);      
                  }
                }, function (response) {
                  toastService.mostarErro('Não foi possivel gravar o arquivo "' + itemUpload.nomeArquivo + '".');
                  removerEnfileiramento(itemUpload.id);
                }, function (evt) {
                  itemUpload.progress = Math.min(100, parseInt(100.0 * evt.loaded / evt.total));
                }).catch(function () {
                  toastService.mostarErro('Não foi possivel gravar o arquivo.');
                });
                // Fechar modal upload
                vm.fechar();
              }

            } else if (isAlteracao) {
              formData = new FormData();
              formData.append('dsFormato', vm.arquivoAnexoDTO.dsFormatoDTO);
              formData.append('dsArquivoAnexo', vm.arquivoAnexoDTO.dsArquivoAnexoDTO);
              formData.append('dsTipoArquivo', model.arquivoAnexo.formatoSelecionado);
              formData.append('idArquivoAcervo', model.idArquivoAcervo);
              formData.append('idArquivoAnexo', vm.arquivoAnexoDTO.idArquivoAnexoDTO ? vm.arquivoAnexoDTO.idArquivoAnexoDTO : "");
              formData.append('coParteAnexo', vm.arquivoAnexoDTO.coParteAnexoDTO ? vm.arquivoAnexoDTO.coParteAnexoDTO : model.indice);

              CadastroDocumentoService.alterarArquivo(formData).then(function (retorno) {
                toastService.mostarSucesso('Registro gravado com sucesso!')
                vm.fechar();
              }).catch(function () {
                toastService.mostarErro('Não foi possivel gravar o arquivo.');
              });

            } else {
              toastService.mostarErro('É necessário anexar um arquivo.');
            }
          } else {
            toastService.mostarErro('É necessário preencher todos os campos obrigatórios.');
          }
        }
      }]);
})();

(function () {
  'use strict';
  var acervoModule = angular.module('su-acervo');

  acervoModule.filter('simNao', function() {
    return function(input) {
      if(input != null) {
        if(input == true) {
          return "Sim";
        } else {
          return "Não";
        }
      }
      return null;
    };
  });

  acervoModule.controller('ModalHistoricoController',
    ['$scope', '$mdDialog', '$state', 'model', 'CadastroDocumentoService', 'toastService',
      function ($scope, $mdDialog, $state, model, CadastroDocumentoService, toastService) {

        var vm = $scope;
        vm.fechar = fechar;

        CadastroDocumentoService.getDescricaoHistorico(model.idHistoricoArquivoAcervo).then(function (retorno) {
          vm.resultado = retorno.data;          
        }).catch(function (erro) {
          toastService.mostarErro('Não foi possivel carregar a descrição do histórico.');
        });

        function fechar() {
          $mdDialog.cancel();
        }

      }]);
})();

(function () {
  'use strict';
  angular.module('su-acervo').controller('HistoricoController',
    ['$scope', '$state', '$stateParams', '$mdDialog', 'CadastroDocumentoService', 'toastService',
      function ($scope,$state, $stateParams, $mdDialog, CadastroDocumentoService, toastService) {

        var vm = $scope;
        vm.bloquearTodosCampos = false;

        if($stateParams.visualizar){
          vm.bloquearTodosCampos = false;
        }

        vm.desabilitarAbaDescritorInformacional = true;
        vm.desabilitarAbaDescritorCatalografico = true;
        vm.desabilitarDescritorTecnico = true;
        vm.desabilitarAbaAnexarArquivo = true;

        CadastroDocumentoService.getOneArquivoAcervo($stateParams.idAcervo).then(function (retorno) {
          vm.arquivoAcervoDTO = retorno.data;
          habilitarAbas(vm.arquivoAcervoDTO);
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar Arquivo Acervo');
        });

        function habilitarAbas(arquivoAcervo){
          vm.desabilitarAbaDescritorInformacional = arquivoAcervo.descritoresInformacionaisDTO ? !arquivoAcervo.descritoresInformacionaisDTO.idDescritorInformacionalDTO : arquivoAcervo.descritoesInformacionaisIconograficoDTO ? !arquivoAcervo.descritoesInformacionaisIconograficoDTO.idDescritoesInformacionaisIconograficoDTO : true;
          vm.desabilitarAbaDescritorCatalografico = arquivoAcervo.descritorCatalograficoDTO ? !arquivoAcervo.descritorCatalograficoDTO.idDescritorCatalograficoDTO : arquivoAcervo.descritorCatalograficoIconograficoDTO ? !arquivoAcervo.descritorCatalograficoIconograficoDTO.idDescritorCatalograficoIconograficoDTO : true;
          vm.desabilitarDescritorTecnico = arquivoAcervo.descritoresTecnicosDTO ? !arquivoAcervo.descritoresTecnicosDTO.idDescritorTecnicoDTO : true;
          vm.desabilitarAbaAnexarArquivo = arquivoAcervo.descritoresTecnicosDTO ? !arquivoAcervo.descritoresTecnicosDTO.idDescritorTecnicoDTO : true;;
        }

        vm.next = next;
        vm.goConsultar = goConsultar;
        vm.previous = previous;
        vm.goDescritoresInformacionais = goDescritoresInformacionais;
        vm.goDescritoresCatalograficos = goDescritoresCatalograficos;
        vm.goDescritorTecnico = goDescritorTecnico;
        vm.abrirDescricaoHistorico = abrirDescricaoHistorico;

        function next() {
          $state.go('ACERVO.EDITAR-DOCUMENTO', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
        }

        function goConsultar() {
          $state.go('ACERVO.CONSULTAR');
        }

        function previous() {
          if($stateParams.visualizar){
            $state.go('ACERVO.ARQUIVO-ANEXO-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.ARQUIVO-ANEXO', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function goDescritoresInformacionais() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-INFORMACIONAIS-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.DESCRITORES-INFORMACIONAIS', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }
        function goDescritoresCatalograficos() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-CATALOGRAFICOS-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else {
            $state.go('ACERVO.DESCRITORES-CATALOGRAFICOS', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function goDescritorTecnico() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-TECNICOS-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.DESCRITORES-TECNICOS', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function abrirDescricaoHistorico(idHistoricoArquivoAcervo){
          $mdDialog.show({
            controller: 'ModalHistoricoController',
            templateUrl: 'scripts-acervo/app/cadastro-documento/view/modal-historico.tmpl.html',
            model: {
              idHistoricoArquivoAcervo: idHistoricoArquivoAcervo
            },
            clickOutsideToClose: false,
            escapeToClose: false
          });
        }

      }]);
})();

(function () {
  'use strict';
  angular.module('su-acervo').controller('DescritoresTecnicosController',
    ['$scope', '$rootScope', '$state', '$stateParams', 'CadastroDocumentoService', 'toastService',
      function ($scope, $rootScope, $state, $stateParams, CadastroDocumentoService, toastService) {

        var vm = $scope;
        var idCategoriaTextual = 1;
        vm.arquivoAcervoDTO = {};

        inicializarDescritorTecnico();

        var bloquearDerivadas = false;
        var arquivoAcervoCarregado = false;
        vm.bloquearTodosCampos = false;

        vm.desabilitarAbaDescritorInformacional = true;
        vm.desabilitarAbaDescritorCatalografico = true;
        vm.desabilitarAbaAnexarArquivo = true;
        vm.desabilitarAbaHitorico = true;

        carregarArquivoAcervo();

        function carregarArquivoAcervo(){
          if($stateParams.visualizar){
            vm.bloquearTodosCampos = true;
          }
          CadastroDocumentoService.getOneArquivoAcervo($stateParams.idAcervo).then(function (retorno) {
            vm.arquivoAcervoDTO = retorno.data;
            habilitarAbas(vm.arquivoAcervoDTO);
            if(vm.arquivoAcervoDTO.descritoresTecnicosDTO){
              tratarArquivoRetornoBanco(vm.arquivoAcervoDTO);
              arquivoAcervoCarregado = true;
            }else{
              inicializarDescritorTecnico();
              arquivoAcervoCarregado = true;
            }
          }).catch(function () {
            toastService.mostarErro('Não foi possivel carregar Arquivo Acervo');
          });
        }

        function habilitarAbas(arquivoAcervo){
          vm.desabilitarAbaDescritorInformacional = arquivoAcervo.descritoresInformacionaisDTO ? !arquivoAcervo.descritoresInformacionaisDTO.idDescritorInformacionalDTO : arquivoAcervo.descritoesInformacionaisIconograficoDTO ? !arquivoAcervo.descritoesInformacionaisIconograficoDTO.idDescritoesInformacionaisIconograficoDTO : true;
          vm.desabilitarAbaDescritorCatalografico = arquivoAcervo.descritorCatalograficoDTO ? !arquivoAcervo.descritorCatalograficoDTO.idDescritorCatalograficoDTO : arquivoAcervo.descritorCatalograficoIconograficoDTO ? !arquivoAcervo.descritorCatalograficoIconograficoDTO.idDescritorCatalograficoIconograficoDTO : true;
          vm.desabilitarAbaAnexarArquivo = arquivoAcervo.descritoresTecnicosDTO ? !arquivoAcervo.descritoresTecnicosDTO.idDescritorTecnicoDTO : true;
          vm.desabilitarAbaHitorico = !arquivoAcervo.idArquivoAcervoDTO;
        }

        function isArquivoAcervoCarregado (){
          return arquivoAcervoCarregado;
        }

        CadastroDocumentoService.getEquipamentoUtilizado().then(function (retorno) {
          vm.equipamentosUtilizados = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar EquipamentosUtilizados');
        });

        CadastroDocumentoService.getDimensaoImgRelacao().then(function (retorno) {
          vm.dimensoesImg = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar EquipamentosUtilizados');
        });

        CadastroDocumentoService.getModoCores().then(function (retorno) {
          vm.modoCores = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar EquipamentosUtilizados');
        });

        CadastroDocumentoService.getResolucaoLinear().then(function (retorno) {
          vm.resolucoesLinear = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar EquipamentosUtilizados');
        });

        CadastroDocumentoService.getResolucaoTonal().then(function (retorno) {
          vm.resolucoesTonal = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar EquipamentosUtilizados');
        });

        function inicializarDescritorTecnico(){
          vm.tela = {};
          vm.arquivoAcervoDTO.descritoresTecnicosDTO = {};
          vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO = {};
          vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO = {};
          vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO = {};
          vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO = false;
          vm.arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO = false;
          vm.arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO = false;
          vm.$parent.dtCadastramentoDTO = new Date();
          CadastroDocumentoService.getUsuarioLogado().then(function (retorno) {
            var dadosUsuario = retorno.data
            if (dadosUsuario){
              vm.arquivoAcervoDTO.descritoresTecnicosDTO.dsRespCadastramentoDTO = dadosUsuario.nome;
            }
          }).catch(function () {
            toastService.mostarErro('Não foi possivel carregar usuário logado');
          });
        }

        vm.goConsultar = goConsultar;
        vm.save = save;
        vm.next = next;
        vm.previous = previous;
        vm.selecionarMatrizTIFF = selecionarMatrizTIFF;
        vm.selecionarDerivadaPDF = selecionarDerivadaPDF;
        vm.selecionarDerivadaJPEG = selecionarDerivadaJPEG;
        vm.isDerivadasBloqueadasPDF = isDerivadasBloqueadasPDF;
        vm.isDerivadasBloqueadasJPEG = isDerivadasBloqueadasJPEG;
        vm.isMatrizBloqueada = isMatrizBloqueada;
        vm.isDerivadasBloqueadas = isDerivadasBloqueadas;
        vm.definirResposavelPelaDerivadas = definirResposavelPelaDerivadas;
        vm.goDescritoresInformacionais = goDescritoresInformacionais;
        vm.goArquivoAcervo = goArquivoAcervo;
        vm.goHistorico = goHistorico;
        vm.isArquivoAcervoCarregado = isArquivoAcervoCarregado;
        vm.formatarDataParaDatePicker = formatarDataParaDatePicker;


        function goConsultar() {
           $state.go('ACERVO.CONSULTAR');
        }

        function goDescritoresInformacionais() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-INFORMACIONAIS-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.DESCRITORES-INFORMACIONAIS', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function goArquivoAcervo() {
          if($stateParams.visualizar){
            $state.go('ACERVO.ARQUIVO-ANEXO-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.ARQUIVO-ANEXO', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function goHistorico() {
          if($stateParams.visualizar){
            $state.go('ACERVO.HISTORICO-ACERVO-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.HISTORICO-ACERVO', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function save(form, goNext) {
          if($stateParams.visualizar){
            next();
          }else{
            form.$setSubmitted();
            if (form.$valid && dataValida()) {
              if(existeMatrizDerivadaSelecionada()){

                tratarArquivoAcervoParaSalvar();

              vm.arquivoAcervoDTO.idTelaOperacao = 3;
              CadastroDocumentoService.save(vm.arquivoAcervoDTO).then(function (retorno) {
                vm.arquivoAcervoDTO = retorno.data;

                  tratarArquivoRetornoBanco(retorno.data);
                  if(vm.arquivoAcervoDTO.categoriaDTO.idCategoriaDTO === idCategoriaTextual){
                    toastService.mostarSucesso('Descritores Técnicos cadastrados com sucesso.')
                  }else{
                    toastService.mostarSucesso('Descritores Técnicos Iconográficos cadastrados com sucesso.')
                  }

                  if (goNext) {
                    next();
                  }
                }).catch(function () {
                  toastService.mostarErro('Erro ao gravar registro!');
                });

              }else{
                toastService.mostarErro('É necessário selecionar ao menos uma matriz ou derivada.');
              }

            } else {
              toastService.mostarErro('É necessário preencher todos os campos obrigatórios.');
            }
          }
        }

        function next() {
          if($stateParams.visualizar){
            $state.go('ACERVO.ARQUIVO-ANEXO-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.ARQUIVO-ANEXO', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function previous() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-CATALOGRAFICOS-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.DESCRITORES-CATALOGRAFICOS', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function dataValida() {
          if(isMatrizBloqueada()){
            return true;
          }
          return vm.tela.dtProdMatrizDTO !== '//' &&
           vm.tela.dtProdMatrizDTO;
        }

        function selecionarMatrizTIFF() {
          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO){
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO = true;
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO = true;

            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO = {};
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO = {};
            vm.tela = {};
            vm.tela.dtProdMatrizDTO = null;

            bloquearDerivadas = true;

          }else{
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.dtProdMatrizDTO = '//';
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO = false;
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO = false;
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO = {};
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO = {};
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO = {};
            vm.tela.dtProdPdfDTO = null;
            vm.tela.dtProdJpegDTO = null;
            $rootScope.$broadcast('limparDiretiva', true);
            bloquearDerivadas = false;
          }
        }

        function selecionarDerivadaPDF(){
          if(!vm.arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO){
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO = {};
            vm.tela.dtProdPdfDTO = null;
          }
        }

        function selecionarDerivadaJPEG(){
          if(!vm.arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO){
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO = {};
            vm.tela = {};
            vm.tela.dtProdJpegDTO = null;
          }
        }


        function isDerivadasBloqueadas() {
          return bloquearDerivadas;
        }

        function isDerivadasBloqueadasPDF() {
          return !vm.arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO;
        }

        function isDerivadasBloqueadasJPEG() {
          return !vm.arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO;
        }

        function isMatrizBloqueada(){
          return !vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO;
        }

        function definirResposavelPelaDerivadas(responsavel){
          vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO.dsRespProdPdfDTO = responsavel;
          vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO.dsRespProdJpegDTO = responsavel;
        }

        function formatarDataParaDatePicker(data){
          if(data){
            var valor = data.toString();
            var numbers = valor.match(/\d+/g);
            return new Date(numbers[0], numbers[1]-1, numbers[2]);
          }
        }

        function existeMatrizDerivadaSelecionada(){
           return vm.arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO ||
                  vm.arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO ||
                  vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO;
        }

        function tratarArquivoAcervoParaSalvar(){

          vm.arquivoAcervoDTO.descritoresTecnicosDTO.dtCadastramentoDTO = vm.dtCadastramentoDTO;

          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO){
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO = vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO ? vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO : null;
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO = vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO ? vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO : null;
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.dtProdMatrizDTO = !vm.tela.dtProdMatrizDTO ? "" : vm.tela.dtProdMatrizDTO;
            return;
          }

          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO){
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO.dtProdPdfDTO = !vm.tela.dtProdPdfDTO ? "" : vm.tela.dtProdPdfDTO;
          }else{
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO = null;
          }

          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO){
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO.dtProdJpegDTO = !vm.tela.dtProdJpegDTO ? "" : vm.tela.dtProdJpegDTO;
          }else{
            vm.arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO = null;
          }
        }

        function tratarArquivoRetornoBanco(retorno){

          var dataRetorno;

          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO){
            dataRetorno = retorno.descritoresTecnicosDTO.infoPdfDTO ? retorno.descritoresTecnicosDTO.infoPdfDTO.dtProdPdfDTO : null;
            vm.tela.dtProdPdfDTO = formatarDataParaDatePicker(dataRetorno);
          }

          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO){
            dataRetorno = retorno.descritoresTecnicosDTO.infoJpegDTO ? retorno.descritoresTecnicosDTO.infoJpegDTO.dtProdJpegDTO : null;
            vm.tela.dtProdJpegDTO = formatarDataParaDatePicker(dataRetorno);
          }

          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO){
            bloquearDerivadas = true;
            dataRetorno = retorno.descritoresTecnicosDTO.infoTiffDTO ? retorno.descritoresTecnicosDTO.infoTiffDTO.dtProdMatrizDTO : null;
            vm.tela.dtProdMatrizDTO = formatarDataParaDatePicker(dataRetorno);
          }

          vm.dtCadastramentoDTO = formatarDataParaDatePicker(retorno.descritoresTecnicosDTO.dtCadastramentoDTO);
        }

      }]);
})();

(function () {
  'use strict';
  angular.module('su-acervo').controller('DescritoresInformacionaisController',
    ['$scope', '$state', '$stateParams', 'CadastroDocumentoService', 'toastService',
      function ($scope, $state, $stateParams, CadastroDocumentoService, toastService) {

        var vm = $scope;

        var idCategoriaTextual = 1;
        var idEspecieDocumentalProcesso = 1;
        var idEspecieDocumentalFichasFinanceirar = 2;
        var isBuscaMunicipioCEP = false;

        vm.desabilitarAbaDescritorCatalografico = true;
        vm.desabilitarAbaDescritorTecnico = true;
        vm.desabilitarAbaAnexarArquivo = true;
        vm.desabilitarAbaHitorico = true;
        vm.termo = "";

        vm.arquivoAcervoDTO = {};
        inicializarDescritorTextual();
        inicializarDescritorIconografico();
        vm.isDataNaoConsta = false;
        vm.isTextual = false;
        vm.isIconografico = false;
        vm.bloquearTodosCampos = false;
        vm.endereco = {};


        CadastroDocumentoService.getCategorias().then(function (retorno) {
          vm.categorias = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar Categorias');
        });

        CadastroDocumentoService.getEspecieDocumentais().then(function (retorno) {
          vm.especieDocumentais = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar Especies documentais');
        });

        CadastroDocumentoService.getAssunto().then(function (retorno) {
          vm.assuntos = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar Assuntos');
        });

        CadastroDocumentoService.getUnidadeFederativa().then(function (retorno) {
          vm.unidadesFederativas = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar Unidades federativas');
        });

        CadastroDocumentoService.getPatrimonio().then(function (retorno) {
          vm.patrimonios =retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar Patrimônios');
        });

        CadastroDocumentoService.getTipoDocumental().then(function (retorno) {
          vm.tiposDocumentais = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar os Tipos Documentais');
        });

        CadastroDocumentoService.getUnidadeMedida().then(function (retorno) {
          vm.unidadesMedida = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar as Unidades de medida.');
        });

        CadastroDocumentoService.getAgrupamentoTematico().then(function (retorno) {
          vm.agrupamentosTematicos = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar os Agrupamentos Temáticos.');
        });

        if ($stateParams.idAcervo) {
          if($stateParams.visualizar){
            vm.bloquearTodosCampos = true;
          }
          CadastroDocumentoService.getOneArquivoAcervo($stateParams.idAcervo).then(function (retorno) {
            vm.arquivoAcervoDTO = retorno.data;
            habilitarAbas(vm.arquivoAcervoDTO);
            if(vm.arquivoAcervoDTO.categoriaDTO.idCategoriaDTO === idCategoriaTextual){
              vm.isTextual = true;
              vm.isIconografico = false;
              if(vm.arquivoAcervoDTO.descritoresInformacionaisDTO.icNaoConstaDataDTO){
                vm.isDataNaoConsta = true;
              }
              if(vm.arquivoAcervoDTO.descritoresInformacionaisDTO.unidadeFederativaDTO){
                var idUnidadeFederativa = vm.arquivoAcervoDTO.descritoresInformacionaisDTO.unidadeFederativaDTO.idUnidadeFederativaDTO;
                if(idUnidadeFederativa){
                  carregarMunicipios(idUnidadeFederativa)
                }
              }
            }else{
              vm.isTextual = false;
              vm.isIconografico = true;

              var idUnidadeFederativa = vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.unidadeFederativaDTO.idUnidadeFederativaDTO;
              if(idUnidadeFederativa){
                carregarMunicipios(idUnidadeFederativa)
              }
            }
          }).catch(function () {
            toastService.mostarErro('Não foi possivel carregar ArquivoAcervo');
          });
        }

        vm.transformChip = transformChip;
        vm.goConsultar = goConsultar;
        vm.onChangeCategoria = onChangeCategoria;
        vm.onChangeUnidadeFederativa = onChangeUnidadeFederativa;
        vm.onChangeMunicipioNaoExiste = onChangeMunicipioNaoExiste;
        vm.isProcesso = isProcesso;
        vm.isFichaFinanceira = isFichaFinanceira;
        vm.save = save;
        vm.next = next;
        vm.querySearch = querySearch;
        vm.goDescritorTecnico = goDescritorTecnico;
        vm.goArquivoAcervo = goArquivoAcervo;
        vm.goHistorico = goHistorico;
        vm.gerarAreaEquivalente = gerarAreaEquivalente;
        vm.onChangeDataConsta = onChangeDataConsta;
        vm.buscarCep = buscarCep;
        vm.searchAssunto = searchAssunto;
        vm.clearSearchTerm = clearSearchTerm;
        vm.transformChipProcessoAntecedente = transformChipProcessoAntecedente
        vm.transformChipProcessoVinculado = transformChipProcessoVinculado

        function goConsultar() {
          $state.go('ACERVO.CONSULTAR');
        }

        function onChangeCategoria() {
          if (vm.arquivoAcervoDTO.categoriaDTO.idCategoriaDTO === idCategoriaTextual) {
            vm.isTextual = true;
            vm.isIconografico = false;
            inicializarDescritorTextual();
          }else{
            vm.isTextual = false;
            vm.isIconografico = true;
            inicializarDescritorIconografico();
          }
        }

        function onChangeMunicipioNaoExiste(municipioNaoExiste, especie){
          if (especie = idEspecieDocumentalProcesso){
            if(municipioNaoExiste){
              vm.arquivoAcervoDTO.descritoresInformacionaisDTO.municipioDTO = null;
            }else{
              vm.arquivoAcervoDTO.descritoresInformacionaisDTO.noMunicipioNaoExistenteDTO = null;
            }
          }else{
            if(municipioNaoExiste){
              vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.municipioDTO = null;
            }else{
              vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.noMunicipioNaoExistenteDTO = null;
            }
          }
        }

        function habilitarAbas(arquivoAcervo){
          vm.desabilitarAbaDescritorCatalografico = arquivoAcervo.descritoresInformacionaisDTO ? !arquivoAcervo.descritoresInformacionaisDTO.idDescritorInformacionalDTO : arquivoAcervo.descritoesInformacionaisIconograficoDTO ? !arquivoAcervo.descritoesInformacionaisIconograficoDTO.idDescritoesInformacionaisIconograficoDTO : true;
          vm.desabilitarAbaDescritorTecnico = arquivoAcervo.descritorCatalograficoDTO ? !arquivoAcervo.descritorCatalograficoDTO.idDescritorCatalograficoDTO : arquivoAcervo.descritorCatalograficoIconograficoDTO ? !arquivoAcervo.descritorCatalograficoIconograficoDTO.idDescritorCatalograficoIconograficoDTO : true;
          vm.desabilitarAbaAnexarArquivo = arquivoAcervo.descritoresTecnicosDTO ? !arquivoAcervo.descritoresTecnicosDTO.idDescritorTecnicoDTO : true;
          vm.desabilitarAbaHitorico = !arquivoAcervo.idArquivoAcervoDTO;
        }

        function gerarAreaEquivalente() {
          if(vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.nuAreaTotalDTO && vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.unidadeMedidaDTO){
            var vlrMetroUnidadeMedida = vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.unidadeMedidaDTO.nuMetroDTO;
            var areaTotal = vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.nuAreaTotalDTO;
            vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.nuAreaEquivalenteDTO = areaTotal * vlrMetroUnidadeMedida;
          }
        }

        function inicializarDescritorIconografico(){
          vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO = {};
          vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listPalavraChaveDTO = [];
          vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listProcessoVinculadoDTO = [];
        }

        function inicializarDescritorTextual(){
          vm.arquivoAcervoDTO.descritoresInformacionaisDTO = {};
          vm.arquivoAcervoDTO.descritoresInformacionaisDTO.palavraChavesDTO = [];
          vm.arquivoAcervoDTO.descritoresInformacionaisDTO.listProcessoAntecedenteDTO = [];
        }

        function onChangeUnidadeFederativa(idUnidadeFederativa){
          carregarMunicipios(idUnidadeFederativa);
        }

        function isProcesso() {
          return vm.arquivoAcervoDTO.descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO
            === idEspecieDocumentalProcesso;
        }

        function isFichaFinanceira() {
          return vm.arquivoAcervoDTO.descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO
            === idEspecieDocumentalFichasFinanceirar;
        }

        function dataValida() {
          if (isFichaFinanceira() || vm.isDataNaoConsta) {
            return true;
          }
          return vm.arquivoAcervoDTO.descritoresInformacionaisDTO.dtAtuacaoProcessoDTO !== '//' &&
            vm.arquivoAcervoDTO.descritoresInformacionaisDTO.dtAtuacaoProcessoDTO;
        }

        function carregarMunicipios(idUnidadeFederativa){
          CadastroDocumentoService.buscarMunicipioPorUnidadeFederativa(idUnidadeFederativa).then(function (retorno) {
            vm.municipios = retorno.data;
            if (isBuscaMunicipioCEP){
              var index = vm.municipios.find(isMunicipioEsperado);
              if(index){
                vm.arquivoAcervoDTO.descritoresInformacionaisDTO.municipioDTO = index
              }
              isBuscaMunicipioCEP = false;
            }
          }).catch(function () {
            toastService.mostarErro('Não foi possivel carregar os Municípios');
          });
        }

        function isUFEsperada(uf){
          return uf.dsUnidadeFederativaDTO == vm.endereco.uf
        }

        function buscarCep(cep){
          CadastroDocumentoService.buscarCep(cep).then(function (retorno) {
            vm.endereco = retorno.data.resposta[0];
            preencherEndereco(vm.endereco)
          }).catch(function () {
            toastService.mostarErro('Não foi possivel encontrar o CEP informado');
          });
        }

        function preencherEndereco(endereco){
          vm.arquivoAcervoDTO.descritoresInformacionaisDTO.dsLogradouroOriginalDTO = vm.endereco.tipoLogradouro + " " + vm.endereco.logradouro
          vm.arquivoAcervoDTO.descritoresInformacionaisDTO.dsBairroDTO = vm.endereco.bairro;
          vm.arquivoAcervoDTO.unidadeFederativaDTO = vm.endereco.uf;
          var index = vm.unidadesFederativas.find(isUFEsperada);
          if (index) {
            isBuscaMunicipioCEP = true;
            vm.arquivoAcervoDTO.descritoresInformacionaisDTO.unidadeFederativaDTO = index;
            carregarMunicipios(vm.arquivoAcervoDTO.descritoresInformacionaisDTO.unidadeFederativaDTO.idUnidadeFederativaDTO)

          }
        }

        function isMunicipioEsperado(municipio){
          return municipio.dsMunicipioDTO == vm.endereco.municipio + " "
        }

        function save(form, goNext) {
          if($stateParams.visualizar){
            next();
          }else{
            form.$setSubmitted();
            if (form.$valid) {
              vm.arquivoAcervoDTO.idArquivoAcervoPaiDTO = vm.arquivoAcervoDTO.idArquivoAcervoPaiDTO ? vm.arquivoAcervoDTO.idArquivoAcervoPaiDTO : $stateParams.idAcervoPai;
              vm.isTextual ? salvarArquivoTextual(goNext) : salvarArquivoIconografico(goNext);
            } else {
              toastService.mostarErro('É necessário preencher todos os campos obrigatórios.');
            }
          }
        }

        function salvarArquivoIconografico(goNext){
          if(verificarCamposObrigatoriosIconografico()){
            vm.arquivoAcervoDTO.descritoresInformacionaisDTO = null;

            vm.arquivoAcervoDTO.idTelaOperacao = 1;
            CadastroDocumentoService.save(vm.arquivoAcervoDTO).then(function (retorno) {
              vm.arquivoAcervoDTO = retorno.data;

              recarregarCampoMultiploAutorOriginal();
              recarregarCampoMultiploAutorCopia();

              toastService.mostarSucesso('Descritores informacionais Iconográficos cadastrados com sucesso')
              if(vm.arquivoAcervoDTO.coProtocoloAcervoDTO){
                toastService.mostrarProtocoloAcervo('Protocolo Acervo: ' + protocoloFormatado(vm.arquivoAcervoDTO.coProtocoloAcervoDTO))
              }
              if (goNext) {
                next();
              }
            }).catch(function () {
              toastService.mostarErro('Erro ao gravar registro!');
            });
          }
        }

        function verificarCamposObrigatoriosIconografico(){
          if(!vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listProcessoVinculadoDTO){
            return true;
          }
          if(!vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listProcessoVinculadoDTO.length || isListaProcessosVinculadosVazia(vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listProcessoVinculadoDTO)){
            if(!vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.icVinculadoSpuDTO){
              return true;
            }
            toastService.mostarErro('É necessário preencher o campo Processo Vinculado.');
            return false;
          }

          return true;
        }

        function isListaProcessosVinculadosVazia(listProcessoVinculadoDTO){
          for(var index in listProcessoVinculadoDTO){
            if(listProcessoVinculadoDTO[index].dsProcessoVinculadoDTO){
              return false;
            }
          }

          return true;
        }

        function salvarArquivoTextual(goNext){
          if (dataValida()) {
            if (vm.arquivoAcervoDTO.descritoresInformacionaisDTO.dsLogradouroOriginalDTO ||
              vm.arquivoAcervoDTO.descritoresInformacionaisDTO.dsConfrontacoesDTO) {

              vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO = null;

              vm.arquivoAcervoDTO.idTelaOperacao = 1;
              CadastroDocumentoService.save(vm.arquivoAcervoDTO).then(function (retorno) {
                vm.arquivoAcervoDTO = retorno.data;

                recarregarCampoMultiploInteressados();

                toastService.mostarSucesso('Descritores informacionais cadastrados com sucesso')
                if(vm.arquivoAcervoDTO.coProtocoloAcervoDTO){
                  toastService.mostrarProtocoloAcervo('Protocolo Acervo: ' + protocoloFormatado(vm.arquivoAcervoDTO.coProtocoloAcervoDTO))
                }
                if (goNext) {
                  next();
                }
              }).catch(function () {
                toastService.mostarErro('Erro ao gravar registro!');
              });

            } else {
              toastService.mostarErro('É necessário preencher "Logradouro Original" ou "Confrontações".');
            }
          } else {
            toastService.mostarErro('É necessário preencher todos os campos obrigatórios.');
          }
        }
        
        function protocoloFormatado(coProtocolo){
          return coProtocolo.substring(0, 4) + '.' + coProtocolo.substring(4, 5) + '.' + coProtocolo.substring(5, 6) + '.' + coProtocolo.substring(6, coProtocolo.length - 2) + "-" + coProtocolo.substr(coProtocolo.length - 2);
        }


        function recarregarCampoMultiploAutorOriginal(){
          if(vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listAutorOriginalDTO.length == 0){
            var object = {
              dsAutorOriginalDTO : null
            };
            vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listAutorOriginalDTO.push(object);
          }
        }

        function recarregarCampoMultiploAutorCopia(){
          if(vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listAutorCopiaDTO.length == 0){
            var object = {
              dsAutorCopiaDTO : null
            };
            vm.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listAutorCopiaDTO.push(object);
          }
        }

        function recarregarCampoMultiploInteressados(){
          if(vm.arquivoAcervoDTO.descritoresInformacionaisDTO.listInteressadosDTO.length == 0){
            var object = {
              dsInteressadosDTO : null
            };
            vm.arquivoAcervoDTO.descritoresInformacionaisDTO.listInteressadosDTO.push(object);
          }
        }

        function next() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-CATALOGRAFICOS-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.DESCRITORES-CATALOGRAFICOS', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function goDescritorTecnico() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-TECNICOS-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.DESCRITORES-TECNICOS', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function goArquivoAcervo() {
          if($stateParams.visualizar){
            $state.go('ACERVO.ARQUIVO-ANEXO-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.ARQUIVO-ANEXO', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function goHistorico() {
          if($stateParams.visualizar){
            $state.go('ACERVO.HISTORICO-ACERVO-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.HISTORICO-ACERVO', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function transformChip(chip) {
          if (angular.isObject(chip)) {
            return chip;
          }
          return {dsPalavraChaveDTO: chip}
        }

        function transformChipProcessoAntecedente(chip) {
          if (angular.isObject(chip)) {
            return chip;
          }
          return {dsProcessoAntecedenteDTO: chip}

        }

        function transformChipProcessoVinculado(chip) {
          if (angular.isObject(chip)) {
            return chip;
          }
          return {dsProcessoVinculadoDTO: chip}

        }

        function querySearch(query) {
          var results = query ? self.states.filter(createFilterFor(query)) : [];
          return results;
        }

        function onChangeDataConsta(){
          if(vm.arquivoAcervoDTO.descritoresInformacionaisDTO.icNaoConstaDataDTO){
            vm.isDataNaoConsta = true
          }
          else{
            vm.isDataNaoConsta = false;
          }
        }

        function clearSearchTerm(){
          vm.termo = "";
        }

        function searchAssunto(ev){
          ev.stopPropagation();
        }

      }]);
})();

(function () {
  'use strict';
  angular.module('su-acervo').controller('DescritoresCatalograficosController',
    ['$scope', '$state', '$stateParams', 'CadastroDocumentoService', 'toastService',
      function ($scope, $state, $stateParams, CadastroDocumentoService, toastService) {

        var vm = $scope;
        var idCategoriaTextual = 1;
        var idEspecieDocumentalProcesso = 1;
        var idEspecieCartografica = 1;
        var idEspecieFotografica = 2;
        var isIconografico = false;
        var isProcesso = false;
        vm.bloquearTodosCampos = false;

        vm.kardexModel = {};

        vm.iconografiaNao = 'N';
        vm.iconografiaSim = 'S';

        vm.arquivoAcervoDTO = {};
        vm.arquivoAcervoDTO.descritorCatalograficoDTO = {};
        vm.arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO = [];
        vm.arquivoAcervoDTO.descritorCatalograficoIconograficoDTO = {};

        vm.desabilitarAbaDescritorInformacional = true;
        vm.desabilitarAbaDescritorTecnico = true;
        vm.desabilitarAbaAnexarArquivo = true;
        vm.desabilitarAbaHitorico = true;

        vm.selected = [];

        if($stateParams.visualizar){
         vm.bloquearTodosCampos = true;
        }

        CadastroDocumentoService.getOneArquivoAcervo($stateParams.idAcervo).then(function (retorno) {
          vm.arquivoAcervoDTO = retorno.data;
          habilitarAbas(vm.arquivoAcervoDTO);
          carregarPorCategoria(vm.arquivoAcervoDTO.categoriaDTO.idCategoriaDTO);

          if(vm.arquivoAcervoDTO.categoriaDTO.idCategoriaDTO !== idCategoriaTextual){
            isIconografico = true;
          }
          if(vm.arquivoAcervoDTO.descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO
            === idEspecieDocumentalProcesso){
            isProcesso = true;
          }
          if(vm.arquivoAcervoDTO.descritorCatalograficoDTO){
            carregarEspecieSelecionada()
          }
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar ArquivoAcervo');
        });

        function carregarEspecieSelecionada() {
          if(vm.arquivoAcervoDTO.descritorCatalograficoDTO.icEspecieCartografiaDTO || vm.arquivoAcervoDTO.descritorCatalograficoDTO.icEspecieFotografiaDTO){
            for(i = 0 ; i < vm.especies.length; i++){
              if(vm.arquivoAcervoDTO.descritorCatalograficoDTO.icEspecieCartografiaDTO){
                if(vm.especies[i].idEspecieDTO == idEspecieCartografica){
                  vm.selected.push(vm.especies[i]);
                  exists(vm.especies[i], vm.selected)
                }
              }
              if(vm.arquivoAcervoDTO.descritorCatalograficoDTO.icEspecieFotografiaDTO){
                if(vm.especies[i].idEspecieDTO == idEspecieFotografica){
                  vm.selected.push(vm.especies[i]);
                  exists(vm.especies[i], vm.selected)
                }
              }
            }
          }
        }

        function habilitarAbas(arquivoAcervo){
          vm.desabilitarAbaDescritorInformacional = arquivoAcervo.descritoresInformacionaisDTO ? !arquivoAcervo.descritoresInformacionaisDTO.idDescritorInformacionalDTO : arquivoAcervo.descritoesInformacionaisIconograficoDTO ? !arquivoAcervo.descritoesInformacionaisIconograficoDTO.idDescritoesInformacionaisIconograficoDTO : true;
          vm.desabilitarAbaDescritorTecnico = arquivoAcervo.descritorCatalograficoDTO ? !arquivoAcervo.descritorCatalograficoDTO.idDescritorCatalograficoDTO : arquivoAcervo.descritorCatalograficoIconograficoDTO ? !arquivoAcervo.descritorCatalograficoIconograficoDTO.idDescritorCatalograficoIconograficoDTO : true;
          vm.desabilitarAbaAnexarArquivo = arquivoAcervo.descritoresTecnicosDTO ? !arquivoAcervo.descritoresTecnicosDTO.idDescritorTecnicoDTO : true;
          vm.desabilitarAbaHitorico = !arquivoAcervo.idArquivoAcervoDTO;
        }

        CadastroDocumentoService.getEspecies().then(function (retorno) {
          vm.especies = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar Especies');
        });

        CadastroDocumentoService.getDocumento().then(function (retorno) {
          vm.documentos = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar os Documentos');
        });

        CadastroDocumentoService.getMaterialSuporte().then(function (retorno) {
          vm.materiaisSuporte = retorno.data;
        }).catch(function () {
          toastService.mostarErro('Não foi possivel carregar os Materiais de Suporte');
        });

        function carregarPorCategoria(idCategoria){
          CadastroDocumentoService.getCondicoesOriginalPorCategoria(idCategoria).then(function (retorno) {
            vm.condicoesOriginal = retorno.data;
          }).catch(function () {
            toastService.mostarErro('Não foi possivel carregar CondicoesOriginal');
          });

          CadastroDocumentoService.getDimensoesSuportePorCategoria(idCategoria).then(function (retorno) {
            vm.dimensoesSuporte = retorno.data;
          }).catch(function () {
            toastService.mostarErro('Não foi possivel carregar DimensoesSuporte');
          });
        }

        vm.previous = previous;
        vm.goConsultar = goConsultar;
        vm.save = save;
        vm.addKardex = addKardex;
        vm.removeKardex = removeKardex;
        vm.goArquivoAcervo = goArquivoAcervo;
        vm.next = next;
        vm.goHistorico = goHistorico;
        vm.isTipoIconografico = isTipoIconografico;
        vm.isTipoProcesso = isTipoProcesso;
        vm.toggle = toggle;
        vm.exists = exists;

        function previous() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-INFORMACIONAIS-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.DESCRITORES-INFORMACIONAIS', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }

        }

        function goConsultar() {
          $state.go('ACERVO.CONSULTAR');
        }

        function save(form, goNext) {
          if($stateParams.visualizar){
            next();
          }else{
            form.$setSubmitted();
            if (form.$valid) {
              isIconografico ? salvarArquivoIconografico(goNext) : salvarArquivoTextual(goNext);
            } else {
              toastService.mostarErro('É necessário preencher todos os campos obrigatórios.');
            }
          }
        }

        function salvarArquivoTextual(goNext) {
          vm.arquivoAcervoDTO.descritorCatalograficoDTO.icEspecieCartografiaDTO = false;
          vm.arquivoAcervoDTO.descritorCatalograficoDTO.icEspecieFotografiaDTO = false;
          if(vm.arquivoAcervoDTO.descritorCatalograficoDTO.icIconografiaDTO === vm.iconografiaSim && vm.selected){
            for(var i = 0; i < vm.selected.length; i ++){
              if(vm.selected[i].idEspecieDTO == idEspecieCartografica){
                vm.arquivoAcervoDTO.descritorCatalograficoDTO.icEspecieCartografiaDTO = true;
              }if(vm.selected[i].idEspecieDTO == idEspecieFotografica){
                vm.arquivoAcervoDTO.descritorCatalograficoDTO.icEspecieFotografiaDTO = true
              }
            }
          }
          if (!vm.arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO ||
            (vm.arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO &&
            vm.arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO.length > 0)) {
            validaKardex();

            vm.arquivoAcervoDTO.descritorCatalograficoIconograficoDTO = null;

            vm.arquivoAcervoDTO.idTelaOperacao = 2;
            CadastroDocumentoService.save(vm.arquivoAcervoDTO).then(function (retorno) {
              vm.arquivoAcervoDTO = retorno.data;
              toastService.mostarSucesso('Descritores Catalográficos cadastrados com sucesso.');
              if (goNext) {
                next();
              }
            }).catch(function () {
              toastService.mostarErro('Erro ao gravar registro.');
            });
          } else {
            toastService.mostarErro('É necessário preencher ao menos uma notação kardex.');
          }
        }

        function salvarArquivoIconografico(goNext){
          vm.arquivoAcervoDTO.descritorCatalograficoDTO = null;

          vm.arquivoAcervoDTO.idTelaOperacao = 2;
          CadastroDocumentoService.save(vm.arquivoAcervoDTO).then(function (retorno) {
            vm.arquivoAcervoDTO = retorno.data;
            toastService.mostarSucesso('Descritores Catalográficos Iconográficos cadastrados com sucesso.');
            if (goNext) {
              next();
            }
          }).catch(function () {
            toastService.mostarErro('Erro ao gravar registro.');
          });
        }

        function isTipoIconografico() {
          return isIconografico;
        }

        function next() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-TECNICOS-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.DESCRITORES-TECNICOS', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function goArquivoAcervo() {
          if($stateParams.visualizar){
            $state.go('ACERVO.ARQUIVO-ANEXO-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.ARQUIVO-ANEXO', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function goHistorico() {
          if($stateParams.visualizar){
            $state.go('ACERVO.HISTORICO-ACERVO-VISUALIZAR', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }else{
            $state.go('ACERVO.HISTORICO-ACERVO', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function addKardex() {
          if (!vm.arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO) {
            vm.arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO = [];
          }
          if (vm.kardexModel.dsArmarioDTO && vm.kardexModel.dsGavetaDTO && vm.kardexModel.dsMacoDTO) {
            vm.arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO.push(vm.kardexModel);
            vm.kardexModel = {};
          } else {
            toastService.mostarErro('Por favor preencha todos os campos da Notação Kardex.');
          }
        }

        function removeKardex(index) {
          $scope.arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO.splice(index, 1);
        }

        function validaKardex() {
          if (!vm.arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO) {
            vm.arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO = [];
          }
        }

        function isTipoProcesso() {
          return isProcesso;
        }

        function toggle (item, list) {
          var idx = list.indexOf(item);
          if (idx > -1) {
            list.splice(idx, 1);
          }
          else {
            list.push(item);
          }
        }
  
        function exists (item, list) {
          return list.indexOf(item) > -1;
        }

      }]);
})();

(function () {
  'use strict';
  angular.module('su-acervo').controller('ArquivoAnexoController',
    ['$window', '$scope', '$state', '$stateParams', '$mdDialog', 'CadastroDocumentoService', 'toastService',
      function ($window, $scope,$state, $stateParams, $mdDialog, CadastroDocumentoService, toastService) {

        var vm = $scope;
        vm.bloquearTodosCampos = false;

        vm.desabilitarAbaDescritorInformacional = true;
        vm.desabilitarAbaDescritorCatalografico = true;
        vm.desabilitarDescritorTecnico = true;
        vm.desabilitarAbaHitorico = true;
        var idEspecieDocumentalFichasFinanceira = 2;
        vm.listaTabelas = [];
 

        listarArquivos();

        function listarArquivos (){
          if($stateParams.visualizar){
            vm.bloquearTodosCampos = true;
          }
          CadastroDocumentoService.getOneArquivoAcervo($stateParams.idAcervo).then(function (retorno) {
            vm.arquivoAcervoDTO = retorno.data;
            habilitarAbas(vm.arquivoAcervoDTO);
            if(vm.arquivoAcervoDTO.listArquivosAnexosDTO.length == 0){
              carregarGradeArquivosAnexarVazia(vm.arquivoAcervoDTO)
            }else{
              carregarGradeArquivosAnexar(vm.arquivoAcervoDTO);
            }
          }).catch(function () {
          });
        }

        function habilitarAbas(arquivoAcervo){
          vm.desabilitarAbaDescritorInformacional = arquivoAcervo.descritoresInformacionaisDTO ? !arquivoAcervo.descritoresInformacionaisDTO.idDescritorInformacionalDTO : arquivoAcervo.descritoesInformacionaisIconograficoDTO ? !arquivoAcervo.descritoesInformacionaisIconograficoDTO.idDescritoesInformacionaisIconograficoDTO : true;
          vm.desabilitarAbaDescritorCatalografico = arquivoAcervo.descritorCatalograficoDTO ? !arquivoAcervo.descritorCatalograficoDTO.idDescritorCatalograficoDTO : arquivoAcervo.descritorCatalograficoIconograficoDTO ? !arquivoAcervo.descritorCatalograficoIconograficoDTO.idDescritorCatalograficoIconograficoDTO : true;
          vm.desabilitarDescritorTecnico = arquivoAcervo.descritoresTecnicosDTO ? !arquivoAcervo.descritoresTecnicosDTO.idDescritorTecnicoDTO : true;
          vm.desabilitarAbaHitorico = !arquivoAcervo.idArquivoAcervoDTO;
        }

        function carregarGradeArquivosAnexar(arquivoAcervo){
          var i = 0;
          while (i < vm.arquivoAcervoDTO.listArquivosAnexosDTO.length){
            vm.listaArquivosAnexos = [];

              if(arquivoAcervo.descritoresTecnicosDTO.icTiffDTO){
                var arquivoGravadoTIFF = selecionarArquivoPeloTipo("image/tiff", tamanhoListaTabelas());
                if(arquivoGravadoTIFF){
                  i ++;
                }
                preencherAnexo(".TIFF", "image/tiff", arquivoGravadoTIFF);
              }
              if(arquivoAcervo.descritoresTecnicosDTO.icPdfDTO){
              var arquivoGravadoPDF = selecionarArquivoPeloTipo("application/pdf", tamanhoListaTabelas());
                if(arquivoGravadoPDF){
                  i ++;
                }
                preencherAnexo(".PDF", "application/pdf", arquivoGravadoPDF);
              }
              if(arquivoAcervo.descritoresTecnicosDTO.icJpegDTO){
              var arquivoGravadoJPEG = selecionarArquivoPeloTipo("image/jpeg", tamanhoListaTabelas());
                if(arquivoGravadoJPEG){
                  i ++;
                }
                preencherAnexo(".JPEG", "image/jpeg", arquivoGravadoJPEG);
              }
              if(!arquivoGravadoTIFF && !arquivoGravadoPDF && !arquivoGravadoJPEG){
                i ++;
              }
              
            if(isAdicionarLista()){
              vm.listaTabelas.push(vm.listaArquivosAnexos);
            }
          }
        }

        function carregarGradeArquivosAnexarVazia(arquivoAcervo){
          vm.listaArquivosAnexos = [];
          
          if(arquivoAcervo.descritoresTecnicosDTO.icTiffDTO){
              preencherAnexo(".TIFF", "image/tiff", null);
          }
          if(arquivoAcervo.descritoresTecnicosDTO.icPdfDTO){
              preencherAnexo(".PDF", "application/pdf", null);
          }
          if(arquivoAcervo.descritoresTecnicosDTO.icJpegDTO){
              preencherAnexo(".JPEG", "image/jpeg", null);
          }
          vm.listaTabelas.push(vm.listaArquivosAnexos);
        }

        function preencherAnexo(formato, formatoSelecionado, arquivoGravado){
          var arquivoAnexo = {};
          arquivoAnexo.idArquivoAnexo = !arquivoGravado ? "" : arquivoGravado.idArquivoAnexoDTO;
          arquivoAnexo.dsTipo = !arquivoGravado  ? "" : arquivoGravado.dsFormatoDTO;
          arquivoAnexo.dsNome = !arquivoGravado  ? "" : arquivoGravado.dsArquivoAnexoDTO;
          arquivoAnexo.dsArquivoAnexo = !arquivoGravado ? "" : arquivoGravado.dsArquivoGravadoDTO;
          arquivoAnexo.dsFormato = formato;
          arquivoAnexo.formatoSelecionado = formatoSelecionado;
          arquivoAnexo.indiceTabelaAnexo = !arquivoGravado ? null : arquivoGravado.coParteAnexoDTO
          if(formato == ".TIFF"){
            arquivoAnexo.mostrarIncluir = vm.bloquearTodosCampos || arquivoGravado ? false : true ;
            arquivoAnexo.mostrarDeletar = vm.bloquearTodosCampos || !arquivoGravado ? false : true;
            arquivoAnexo.mostrarEditar = vm.bloquearTodosCampos || !arquivoGravado ? false : true;
          }else{
            arquivoAnexo.mostrarIncluir = vm.bloquearTodosCampos || arquivoGravado || vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO ? false : true;
            arquivoAnexo.mostrarDeletar = vm.bloquearTodosCampos || !arquivoGravado || vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO ? false : true;
            arquivoAnexo.mostrarEditar = vm.bloquearTodosCampos || !arquivoGravado || vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO ? false : true;
          }   
          arquivoAnexo.mostrarVisualizar = !arquivoGravado ? false : true;
          vm.listaArquivosAnexos.push(arquivoAnexo);
        }

        vm.incluirNovoArquivo = incluirNovoArquivo;
        vm.alterarNovoArquivo = alterarNovoArquivo;
        vm.donwloadArquivoAnexo = donwloadArquivoAnexo;
        vm.deletarArquivoAnexo = deletarArquivoAnexo;
        vm.next = next;
        vm.goConsultar = goConsultar;
        vm.previous = previous;
        vm.goDescritoresInformacionais = goDescritoresInformacionais;
        vm.goDescritoresCatalograficos = goDescritoresCatalograficos;
        vm.goHistorico = goHistorico;
        vm.add = add;
        vm.isArquivoAnteriorNaoPreenchido = isArquivoAnteriorNaoPreenchido;

        function selecionarArquivoPeloTipo(tipo, indiceParteArquivo){
          for(var index in vm.arquivoAcervoDTO.listArquivosAnexosDTO){  
            if(vm.arquivoAcervoDTO.listArquivosAnexosDTO[index].dsTipoArquivoDTO.localeCompare(tipo) == 0 && vm.arquivoAcervoDTO.listArquivosAnexosDTO[index].coParteAnexoDTO == indiceParteArquivo){
              return vm.arquivoAcervoDTO.listArquivosAnexosDTO[index];
            }
          }
        }

        function verificarSeFoiAnexado(){
          if(!vm.arquivoAcervoDTO.descritoresTecnicosDTO){
            toastService.mostarErro('Descritor técnico não cadastrado.');
            return false;
          }else if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO){
            if(!selecionarArquivoPeloTipo("image/tiff"), 0){
              toastService.mostarErro('É necessário inserir o arquivo TIF');
              return false;
            }
          }

          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO){
            if(!selecionarArquivoPeloTipo("application/pdf"), 0){
              toastService.mostarErro('É necessário inserir o arquivo PDF');
              return false;
            }
          }

          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO){
            if(!selecionarArquivoPeloTipo("image/jpeg"), 0){
              toastService.mostarErro('É necessário inserir o arquivo JPEG');
              return false;
            }
          }
          return true;
        }

        function incluirNovoArquivo(arquivoAnexo, indice) {
          $mdDialog.show({
            controller: 'ModalNovoArquivoAnexoController',
            templateUrl: 'scripts-acervo/app/cadastro-documento/view/modal-novo-arquivo-anexo.tmpl.html',
            model: {
              idArquivoAcervo: vm.arquivoAcervoDTO.idArquivoAcervoDTO,
              arquivoAnexo: arquivoAnexo,
              isFichaFinanceira: isTipoFichaFinanceira(),
              callback: retornoAdicaoAlteracaoAnexo,
              indice: indice
            },
            clickOutsideToClose: false,
            escapeToClose: false           
          });
        }

        function isTipoFichaFinanceira(){
          return (vm.arquivoAcervoDTO.descritoresInformacionaisDTO &&
            vm.arquivoAcervoDTO.descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO
          === idEspecieDocumentalFichasFinanceira);
        }


        function alterarNovoArquivo(arquivoAnexo, indice){
          $mdDialog.show({
            controller: 'ModalNovoArquivoAnexoController',
            templateUrl: 'scripts-acervo/app/cadastro-documento/view/modal-novo-arquivo-anexo.tmpl.html',
            model: {
              idArquivoAcervo : vm.arquivoAcervoDTO.idArquivoAcervoDTO,
              arquivoAnexo: arquivoAnexo,
              callback: retornoAdicaoAlteracaoAnexo,
              indice: indice
            },
            clickOutsideToClose: false,
            escapeToClose: false           
          });
        }

        function donwloadArquivoAnexo(idArquivoAnexo){
          $window.location.href = "/acervo/api/arquivo-anexo/" + idArquivoAnexo;
        }

        function deletarArquivoAnexo(idArquivoAnexo){
          var confirm = $mdDialog.confirm()
            .title('Confirmação')
            .textContent('Tem certeza que deseja excluir este registro?')
            .ok('Confirmar')
            .cancel('Cancelar');

          $mdDialog.show(confirm).then(function () {
            CadastroDocumentoService.deletarArquivoAnexo(idArquivoAnexo).then(function () {
              vm.listaTabelas = [];
              listarArquivos();
            }).catch(function () {
              toastService.mostarErro('Não foi possivel excluir Arquivo Acervo');
            });
          }, function () {

          });
        }

        function next() {
          if(verificarSeFoiAnexado()){
            $state.go('ACERVO.EDITAR-DOCUMENTO', {'idAcervo': vm.arquivoAcervoDTO.idArquivoAcervoDTO});
          }
        }

        function goConsultar() {
          $state.go('ACERVO.CONSULTAR');
        }

        function previous() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-TECNICOS-VISUALIZAR', {'idAcervo': $stateParams.idAcervo});
          }else{
            $state.go('ACERVO.DESCRITORES-TECNICOS', {'idAcervo': $stateParams.idAcervo});
          }
        }

        function goDescritoresInformacionais() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-INFORMACIONAIS-VISUALIZAR', {'idAcervo': $stateParams.idAcervo});
          }else{
            $state.go('ACERVO.DESCRITORES-INFORMACIONAIS', {'idAcervo': $stateParams.idAcervo});
          }

        }
        function goDescritoresCatalograficos() {
          if($stateParams.visualizar){
            $state.go('ACERVO.DESCRITORES-CATALOGRAFICOS-VISUALIZAR', {'idAcervo': $stateParams.idAcervo});
          }else{
            $state.go('ACERVO.DESCRITORES-CATALOGRAFICOS', {'idAcervo': $stateParams.idAcervo});
          }
        }

        function goHistorico() {
          if($stateParams.visualizar){
            $state.go('ACERVO.HISTORICO-ACERVO-VISUALIZAR', {'idAcervo': $stateParams.idAcervo});
          }else{
            $state.go('ACERVO.HISTORICO-ACERVO', {'idAcervo': $stateParams.idAcervo});
          }
        }

        function add(){
          carregarGradeArquivosAnexarVazia(vm.arquivoAcervoDTO)
        }

        function isArquivoAnteriorNaoPreenchido(){
          var ultimoIndiceTabela = tamanhoListaTabelas();
          if(ultimoIndiceTabela >= 1){
            for(var index in vm.listaTabelas[ultimoIndiceTabela -1]){
              if(vm.listaTabelas[ultimoIndiceTabela -1][index].indiceTabelaAnexo != null){
                return true;
              }
            }
            return false;
          }
        }

        function tamanhoListaTabelas(){
          return vm.listaTabelas.length ? vm.listaTabelas.length : 0;
        }

        function isAdicionarLista(){
          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO){
            var count = 0;
            for(var index in vm.listaArquivosAnexos){
              if (vm.listaArquivosAnexos[index].dsFormato == ".TIFF"){
                count ++;
              }
            }
            if(count < 1){
              return false;
            }
          }
          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO){
            var count = 0;
            for(var index in vm.listaArquivosAnexos){
              if (vm.listaArquivosAnexos[index].dsFormato == ".JPEG"){
                count ++;
              }
            }
            if(count < 1){
              return false;
            }
          }
          if(vm.arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO){
            var count = 0;
            for(var index in vm.listaArquivosAnexos){
              if (vm.listaArquivosAnexos[index].dsFormato == ".PDF"){
                count ++;
              }
            }
            if(count < 1){
              return false;
            }
          }
          return true
        }

        function retornoAdicaoAlteracaoAnexo(){
          vm.listaTabelas = [];
          listarArquivos();
        }

      }]);
})();

(function () {
    'use strict';
  
    service.$inject = ["$http"];
    function service($http) {
  
      var baseURL = '/acervo/api';
      
      var listarArquivoAcervoParaAssociacao = function (numProtocolo){
        return $http.get( baseURL + '/arquivo-acervo/listar-hierarquia-associacao/' + numProtocolo);
      };

      var buscarAcervoAssociacao = function (numProtocolo){
        return $http.get( baseURL + '/arquivo-acervo/consultar-protocolo-associacao/' + numProtocolo);
      };
      
      var listarArquivosAcervoAssociados = function (idAcervo){
        return $http.get( baseURL + '/arquivo-acervo/listar-associados/' + idAcervo);
      };

      var associarAcervo = function (data){
        return $http.post( baseURL + '/arquivo-acervo/associar-acervo/', data);
      };
  
      return {
        listarArquivoAcervoParaAssociacao: listarArquivoAcervoParaAssociacao,
        buscarAcervoAssociacao: buscarAcervoAssociacao,
        listarArquivosAcervoAssociados: listarArquivosAcervoAssociados,
        associarAcervo: associarAcervo
      }
    }
  
    angular.module('su-acervo').factory('AssociarDocumentoService', service);
  
  })();
  
(function () {
    'use strict';
    angular.module('su-acervo').controller('ProtocoloAssociarController',
      ['$scope', '$state', '$stateParams', '$mdDialog', 'AssociarDocumentoService', 'CadastroDocumentoService', 'toastService',
        function ($scope, $state, $stateParams, $mdDialog, AssociarDocumentoService, CadastroDocumentoService, toastService) {
  
          var vm = $scope;
          vm.numProtocolo = "";
          vm.showListaAcervo = false;
          vm.listaArquivosAcervos = [];

          function buscarProtocolo(){
            if(vm.numProtocolo == ""){
              toastService.mostarErro("Um número de protocolo deve ser informado");
              return;
            }
            vm.numProtocoloBuscado = vm.numProtocolo;
            carregarListaArquivosAcervoMaisFilhos(vm.numProtocolo);
          }

          function carregarListaArquivosAcervoMaisFilhos(numProtocolo){
            AssociarDocumentoService.listarArquivoAcervoParaAssociacao(numProtocolo).then(function (response) {
              vm.listaArquivosAcervos = response.data;
              if(vm.listaArquivosAcervos.length == 0){
                toastService.mostarErro("Nenhum acervo encontrado.");
                return;
              }
              vm.showListaAcervo = true;
            }).catch(function () {
              toastService.mostarErro("Erro ao carregar lista de acervos.");
            });
          }

          function editar(idArquivoAcervo) {
            $state.go('ACERVO.ASSOCIAR', {'idAcervo': idArquivoAcervo});
        }

        function visualizar(idArquivoAcervo){
          $state.go('ACERVO.ASSOCIAR-VISUALIZAR', {'idAcervo': idArquivoAcervo});
        }

        function goVisualizarDocumento(id) {
          $state.go('ACERVO.DESCRITORES-INFORMACIONAIS-VISUALIZAR', {'idAcervo': id});
        }
          

          vm.buscarProtocolo = buscarProtocolo;
          vm.editar = editar;
          vm.visualizar = visualizar;
          vm.goVisualizarDocumento = goVisualizarDocumento;
          
        }]);
  })();
  
(function () {
    'use strict';
    angular.module('su-acervo').controller('AssociarDocumentoController',
      ['$scope', '$state', '$stateParams', '$mdDialog', 'AssociarDocumentoService', 'CadastroDocumentoService', 'toastService',
        function ($scope, $state, $stateParams, $mdDialog, AssociarDocumentoService, CadastroDocumentoService, toastService) {
  
          carregarListaAcervosAssociados($stateParams.idAcervo)

          var vm = $scope;
          vm.numProtocoloAssociacao = "";
          vm.showAssociarProtocolo = false;
          vm.showListaAssociarProtocolo = false;
          vm.listaArquivosAcervos = [];
          vm.arquivoAcervoAssociacao = {};
          vm.arquivoAcervoDTO = {};
          vm.indexRemover = null;
          vm.bloquearTodosCampos = false;

          if($stateParams.visualizar){
            vm.bloquearTodosCampos = true;
           }
          

          function carregarListaAcervosAssociados(idAcervo){
            AssociarDocumentoService.listarArquivosAcervoAssociados(idAcervo).then(function (response) {
              vm.listaArquivosAcervos = response.data;
              if(vm.listaArquivosAcervos.length == 0){
                toastService.mostarErro("Nenhum acervo encontrado.");
                return;
              }
            }).catch(function () {
              toastService.mostarErro("Erro ao carregar lista de acervos.");
            });
          }
          

          function carregarAcervoParaAssociacao(numProtocolo){
            AssociarDocumentoService.buscarAcervoAssociacao(numProtocolo).then(function (response) {
              vm.arquivoAcervoAssociacao = response.data;
              if(vm.arquivoAcervoAssociacao.idArquivoAcervoDTO == null){
                toastService.mostarErro("Nenhum acervo encontrado.");
                return;
              }
              vm.showListaAssociarProtocolo = true;
            }).catch(function () {
              toastService.mostarErro("Erro ao carregar acervo.");
            });
          }


          function openBuscarProtocolo(){
            vm.showAssociarProtocolo = true;
          }

          function buscarProtocoloAssociacao(){
            if(vm.numProtocoloAssociacao == ""){
              toastService.mostarErro("Um número de protocolo deve ser informado");
              return;
            }
            carregarAcervoParaAssociacao(vm.numProtocoloAssociacao);
          }

          function mostrarInformacoesArquivoAnexo(arquivoAcervoDTO){
            if(arquivoAcervoDTO && arquivoAcervoDTO.listArquivosAnexosDTO && arquivoAcervoDTO.listArquivosAnexosDTO.length > 0){
              if(!arquivoAcervoDTO.descritoresTecnicosDTO){
                return { nome: "", tipo: "", formato: "" };
              }
              var arquivoAcervoPai = {};
              
              if(arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO){
                for(var index in arquivoAcervoDTO.listArquivosAnexosDTO){
                  if(arquivoAcervoDTO.listArquivosAnexosDTO[index].dsTipoArquivoDTO.localeCompare("image/tiff") === 0){
                    arquivoAcervoPai.nome = arquivoAcervoDTO.listArquivosAnexosDTO[index].dsArquivoAnexoDTO;
                    arquivoAcervoPai.tipo = arquivoAcervoDTO.listArquivosAnexosDTO[index].dsFormatoDTO;
                    arquivoAcervoPai.formato = arquivoAcervoDTO.listArquivosAnexosDTO[index].dsTipoArquivoDTO;
                  }
                }
              }else {
                arquivoAcervoPai.nome = arquivoAcervoDTO.listArquivosAnexosDTO[0].dsArquivoAnexoDTO;
                arquivoAcervoPai.tipo = arquivoAcervoDTO.listArquivosAnexosDTO[0].dsFormatoDTO;
                if(arquivoAcervoDTO.listArquivosAnexosDTO.length >= 2){
                  arquivoAcervoPai.formato = arquivoAcervoDTO.listArquivosAnexosDTO[0].dsTipoArquivoDTO + ", " + arquivoAcervoDTO.listArquivosAnexosDTO[1].dsTipoArquivoDTO;
                }else{
                  arquivoAcervoPai.formato = arquivoAcervoDTO.listArquivosAnexosDTO[0].dsTipoArquivoDTO
                }
              }
              return arquivoAcervoPai;
            }   
          }

          function associarAcervo(acervo){
            for(var index in vm.listaArquivosAcervos){
              if(vm.listaArquivosAcervos[index].idArquivoAcervoDTO == $stateParams.idAcervo){
                if(vm.listaArquivosAcervos[index].idArquivoAcervoDTO == acervo.idArquivoAcervoDTO){
                  toastService.mostarErro("O acervo informado já está associado");
                  return;
                }else{
                  for(var i in vm.listaArquivosAcervos[index].listaAcervosAssociadosDTO){
                    if(vm.listaArquivosAcervos[index].listaAcervosAssociadosDTO[i].idArquivoAcervoDTO == acervo.idArquivoAcervoDTO){
                      toastService.mostarErro("O acervo informado já está associado");
                      return;
                    }
                  }
                }
                vm.listaArquivosAcervos[index].listaAcervosAssociadosDTO.push(acervo)
                AssociarDocumentoService.associarAcervo(vm.listaArquivosAcervos[index]).then(function (retorno) {
                  vm.arquivoAcervoDTO = retorno.data;
                  carregarListaAcervosAssociados(vm.arquivoAcervoDTO.idArquivoAcervoDTO)
                  return;
                }).catch(function () {
                  toastService.mostarErro("O acervo não pode ser associado.");
                });
              }
            }
            vm.showAssociarProtocolo = false;
            vm.showListaAssociarProtocolo = false;
          }

          

          function removerAssociacao(arquivoAcervo){
            for(var index in vm.listaArquivosAcervos){
              if(vm.listaArquivosAcervos[index].idArquivoAcervoDTO == $stateParams.idAcervo){
                for(var i in vm.listaArquivosAcervos[index].listaAcervosAssociadosDTO){
                  if(vm.listaArquivosAcervos[index].listaAcervosAssociadosDTO[i].idArquivoAcervoDTO == arquivoAcervo.idArquivoAcervoDTO){
                    vm.listaArquivosAcervos[index].listaAcervosAssociadosDTO.splice(i, 1)
                    CadastroDocumentoService.save(vm.listaArquivosAcervos[index]).then(function (retorno) {
                      vm.arquivoAcervoDTO = retorno.data;
                      carregarListaAcervosAssociados($stateParams.idAcervo)
                      return;
                    });
                  }
                }
                for(var j in vm.listaArquivosAcervos[index].listaArquivosAcervosFilhosDTO){
                  if(vm.listaArquivosAcervos[index].listaArquivosAcervosFilhosDTO[j].idArquivoAcervoDTO == arquivoAcervo.idArquivoAcervoDTO){
                    vm.listaArquivosAcervos[index].listaArquivosAcervosFilhosDTO[j].idArquivoAcervoPaiDTO = null
                    CadastroDocumentoService.save(vm.listaArquivosAcervos[index].listaArquivosAcervosFilhosDTO[j]).then(function (retorno) {
                      vm.arquivoAcervoDTO = retorno.data;
                      carregarListaAcervosAssociados($stateParams.idAcervo)
                      return;
                    });
                  }
                }
              }
            }
          }

          function goVisualizarDocumento(id) {
            $state.go('ACERVO.DESCRITORES-INFORMACIONAIS-VISUALIZAR', {'idAcervo': id});
          }

          vm.openBuscarProtocolo = openBuscarProtocolo;
          vm.buscarProtocoloAssociacao = buscarProtocoloAssociacao;
          vm.mostrarInformacoesArquivoAnexo = mostrarInformacoesArquivoAnexo;
          vm.associarAcervo = associarAcervo;
          vm.removerAssociacao = removerAssociacao;
          vm.goVisualizarDocumento = goVisualizarDocumento;
  
        }]);
  })();
  
(function () {

  var baseURL = 'scripts-acervo/app';

  'use strict';

  angular.module('su-acervo').config(["$stateProvider", function ($stateProvider) {
    $stateProvider
      .state('ACERVO', {
        abstract: true,
        // template: '<div id="su-acervo" ui-view></div>',
        templateUrl: baseURL + '/template/template.html',
        url: '/acervo'
      })

      .state('ACERVO.CONSULTAR', {
        url: '/consultar',
        templateUrl: baseURL + '/consultar/view/consultar.tmpl.html',
        controller: 'ConsultarController as vm',
        ncyBreadcrumb: {
          label: 'CONSULTAR ACERVOS',
          parent: 'ACERVO'
        }
      })

      .state('ACERVO.DESCRITORES-INFORMACIONAIS', {
        url: '/descritores-informacionais/:idAcervo?:idAcervoPai',
        templateUrl: baseURL + '/cadastro-documento/view/descritores-informacionais.tmpl.html',
        controller: 'DescritoresInformacionaisController as vm',
        ncyBreadcrumb: {
          label: 'CADASTRO DE DESCRITORES INFORMACIONAIS',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.DESCRITORES-CATALOGRAFICOS', {
        url: '/descritores-catalograficos/:idAcervo',
        templateUrl: baseURL + '/cadastro-documento/view/descritores-catalograficos.tmpl.html',
        controller: 'DescritoresCatalograficosController as vm',
        ncyBreadcrumb: {
          label: 'CADASTRO DE DESCRITORES CATALOGRÁFICOS',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.DESCRITORES-TECNICOS', {
        url: '/descritores-tecnicos/:idAcervo',
        templateUrl: baseURL + '/cadastro-documento/view/descritores-tecnicos.tmpl.html',
        controller: 'DescritoresTecnicosController as vm',
        ncyBreadcrumb: {
          label: 'CADASTRO DE DESCRITORES TÉCNICOS',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.EDITAR-DOCUMENTO', {
        url: '/editar-documento/:idAcervo',
        templateUrl: baseURL + '/editar-documento/view/editar-documento.tmpl.html',
        controller: 'EditarDocumentoController as vm',
        ncyBreadcrumb: {
          label: 'GERENCIAR ARQUIVO ACERVO',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.ARQUIVO-ANEXO', {
        url: '/arquivo-anexo/:idAcervo',
        templateUrl: baseURL + '/cadastro-documento/view/arquivo-anexo.tmpl.html',
        controller: 'ArquivoAnexoController as vm',
        ncyBreadcrumb: {
          label: 'ANEXAR ARQUIVO',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.HISTORICO-ACERVO', {
        url: '/historico-acervo/:idAcervo',
        templateUrl: baseURL + '/cadastro-documento/view/historico-acervo.tmpl.html',
        controller: 'HistoricoController as vm',
        ncyBreadcrumb: {
          label: 'HISTÓRICO ACERVO',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.DESCRITORES-INFORMACIONAIS-VISUALIZAR', {
        url: '/descritores-informacionais/visualizar/:idAcervo',
        templateUrl: baseURL + '/cadastro-documento/view/descritores-informacionais.tmpl.html',
        controller: 'DescritoresInformacionaisController as vm',
        params : {
          visualizar : true
        },
        ncyBreadcrumb: {
          label: 'VISUALIZAR DESCRITORES INFORMACIONAIS',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.DESCRITORES-CATALOGRAFICOS-VISUALIZAR', {
        url: '/descritores-catalograficos/visualizar/:idAcervo',
        templateUrl: baseURL + '/cadastro-documento/view/descritores-catalograficos.tmpl.html',
        controller: 'DescritoresCatalograficosController as vm',
        params : {
          visualizar : true
        },
        ncyBreadcrumb: {
          label: 'VISUALIZAR DESCRITORES CATALOGRÁFICOS',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.DESCRITORES-TECNICOS-VISUALIZAR', {
        url: '/descritores-tecnicos/visualizar/:idAcervo',
        templateUrl: baseURL + '/cadastro-documento/view/descritores-tecnicos.tmpl.html',
        controller: 'DescritoresTecnicosController as vm',
        params : {
          visualizar : true
        },
        ncyBreadcrumb: {
          label: 'VISUALIZAR DESCRITORES TÉCNICOS',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.ARQUIVO-ANEXO-VISUALIZAR', {
        url: '/arquivo-anexo/visualizar/:idAcervo',
        templateUrl: baseURL + '/cadastro-documento/view/arquivo-anexo.tmpl.html',
        controller: 'ArquivoAnexoController as vm',
        params : {
          visualizar : true
        },
        ncyBreadcrumb: {
          label: 'VISUALIZAR ARQUIVO ANEXO',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.HISTORICO-ACERVO-VISUALIZAR', {
        url: '/historico-acervo/visualizar/:idAcervo',
        templateUrl: baseURL + '/cadastro-documento/view/historico-acervo.tmpl.html',
        controller: 'HistoricoController as vm',
        params : {
          visualizar : true
        },
        ncyBreadcrumb: {
          label: 'HISTÓRICO ACERVO',
          parent: 'ACERVO.CONSULTAR'
        }
      })

      .state('ACERVO.ASSOCIAR-VISUALIZAR', {
        url: '/associar-documento/visualizar/:idAcervo',
        templateUrl: baseURL + '/associar-documento/view/associar-documento.tmpl.html',
        controller: 'AssociarDocumentoController as vm',
        params : {
          visualizar : true
        },
        ncyBreadcrumb: {
          label: 'ASSOCIAR OU DESASSOCIAR ACERVOS',
          parent: 'ACERVO.PROTOCOLO-ASSOCIAR'
        }
      })

      .state('ACERVO.ASSOCIAR', {
        url: '/associar-documento/:idAcervo',
        templateUrl: baseURL + '/associar-documento/view/associar-documento.tmpl.html',
        controller: 'AssociarDocumentoController as vm',
        ncyBreadcrumb: {
          label: 'ASSOCIAR OU DESASSOCIAR ACERVOS',
          parent: 'ACERVO.PROTOCOLO-ASSOCIAR'
        }
      })

      .state('ACERVO.PROTOCOLO-ASSOCIAR', {
        url: '/protocolo-associar',
        templateUrl: baseURL + '/associar-documento/view/protocolo-associar.tmpl.html',
        controller: 'ProtocoloAssociarController as vm',
        ncyBreadcrumb: {
          label: 'BUSCAR PROTOCOLO ASSOCIAR',
          parent: 'ACERVO'
        }
      });

  }]);

})();

angular.module("su-acervo").run(["$templateCache", function($templateCache) {$templateCache.put("scripts-acervo/app/template/template.html","<div id=su-acervo ui-view></div><!-- UPLOAD de arquivos --><div class=su-acervo-fila-upload ng-show=\"$root.uploads.length > 0\"><div layout=column layout-align=\"center space-arround\" class=painel-upload><!-- Repetir --><div class=\"painel-upload-item md-container\" ng-repeat=\"upload in $root.uploads track by $index\"><div flex=100 class=painel-upload-item-descricao ng-if=upload.upload>Upload do arquivo: \"{{upload.nomeArquivo}}\"</div><div flex=100 class=painel-upload-item-descricao ng-if=!upload.upload>Conversão do arquivo: \"{{upload.nomeArquivo}}\"</div><div class=painel-upload-item-progress flex=100><md-progress-linear flex=100 md-mode=determinate value={{upload.progress}} ng-if=upload.upload></md-progress-linear><md-progress-linear flex=100 md-mode=indeterminate ng-if=!upload.upload></md-progress-linear></div></div></div></div>");
$templateCache.put("scripts-acervo/app/associar-documento/view/associar-documento.tmpl.html","<div><fieldset><legend>Associar ou Desassociar Protocolos Acervo</legend><h2>Associar ou Desassociar Protocolos Acervo</h2><div layout=row layout-align=\"center none\"><div flex=100><md-table-container><table md-table><thead md-head><tr md-row><th md-column>Protocolo Acervo</th><th md-column>Tipo de Documento</th><th md-column>Nome do Documento</th><th md-column>Documento Principal</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"arquivoAcervo in listaArquivosAcervos | filter: {situacaoDTO : {idSituacaoDTO: 1}}\"><td md-cell><a ng-click=goVisualizarDocumento(arquivoAcervo.idArquivoAcervoDTO) class=link-resultados-obtidos><span>{{arquivoAcervo.coProtocoloAcervoDTOFormatada}}</span></a></td><td md-cell>{{mostrarInformacoesArquivoAnexo(arquivoAcervo).tipo}}</td><td md-cell>{{mostrarInformacoesArquivoAnexo(arquivoAcervo).nome}}</td><td md-cell><md-checkbox id=checkBoxPrincipal{{$index}} ng-model=arquivoAcervo.exibirPrincipalDTO class=md-primary disabled></td><td md-cell layout-align=\"center center\" style=\"width: 20%\"><md-tooltip ng-if=arquivoAcervo.exibirPrincipalDTO md-direction=bottom>Não é possível desassociar documento principal</md-tooltip><md-button id=botaoAssociarAcervo ng-click=removerAssociacao(arquivoAcervo) class=md-raised ng-disabled=\"arquivoAcervo.exibirPrincipalDTO || listaArquivosAcervos.length == 1 || bloquearTodosCampos\">Remover associação</md-button></td></tr></tbody></table></md-table-container><div style=\"text-align: right; margin-top:30px\"><md-button id=botaoNovoAcervo class=md-raised ng-click=openBuscarProtocolo() ng-disabled=bloquearTodosCampos><md-icon md-font-set=material-icons>add</md-icon>Associar novo protocolo na relação</md-button></div></div></div><div layout=row layout-align=\"start center\" ng-show=showAssociarProtocolo><md-input-container flex=30><label>Buscar protocolo para associar à relação</label><input type=text ng-model=numProtocoloAssociacao mask=9999.9.9.99999-99 clean=true></md-input-container><md-button id=botaoPesquisar class=md-raised ng-click=buscarProtocoloAssociacao() ng-disabled=bloquearTodosCampos><md-icon md-font-set=material-icons>search</md-icon>Consultar</md-button></div><div layout=row layout-align=\"center none\" ng-show=showListaAssociarProtocolo><div flex=100><md-table-container><table md-table><thead md-head><tr md-row><th md-column>Protocolo Acervo</th><th md-column>Tipo de Documento</th><th md-column>Nome do Documento</th><th md-column>Documento Principal</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row><td md-cell>{{arquivoAcervoAssociacao.coProtocoloAcervoDTOFormatada}}</td><td md-cell>{{mostrarInformacoesArquivoAnexo(arquivoAcervoAssociacao).tipo}}</td><td md-cell>{{mostrarInformacoesArquivoAnexo().nome}}</td><td md-cell><md-checkbox id=checkBoxPrincipal{{$index}} ng-model=arquivoAcervoAssociacao.icPrincipalDTO class=md-primary disabled></td><td md-cell layout-align=\"center center\" style=\"width: 20%\"><md-button id=botaoAssociarAcervo class=md-raised ng-click=associarAcervo(arquivoAcervoAssociacao) ng-disabled=bloquearTodosCampos>Associar à relação</md-button></td></tr></tbody></table></md-table-container></div></div></fieldset></div>");
$templateCache.put("scripts-acervo/app/associar-documento/view/protocolo-associar.tmpl.html","<div><fieldset><legend>Associar ou Desassociar Protocolos Acervo</legend><h2>Associar ou Desassociar Protocolos Acervo</h2><div layout=row layout-align=\"start center\"><md-input-container flex=30><label>Buscar protocolo</label><input type=text ng-model=numProtocolo mask=9999.9.9.99999-99 clean=true></md-input-container><md-button id=botaoPesquisar class=md-raised ng-click=buscarProtocolo()><md-icon md-font-set=material-icons>search</md-icon>Consultar</md-button></div><div layout=row layout-align=\"center none\" ng-show=showListaAcervo><div flex=100><md-table-container><table md-table><thead md-head><tr md-row><th md-column>Protocolo Acervo</th><th md-column>Tipo de Documento</th><th md-column>Nome do Documento</th><th md-column>Documento Principal</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"arquivoAcervo in listaArquivosAcervos | filter: {situacaoDTO : {idSituacaoDTO: 1}}\"><td md-cell><a ng-click=goVisualizarDocumento(arquivoAcervo.idArquivoAcervoDTO) class=link-resultados-obtidos><span>{{arquivoAcervo.coProtocoloAcervoDTOFormatada}}</span></a></td><td md-cell>{{arquivoAcervo.tipoExibirDTO}}</td><td md-cell>{{arquivoAcervo.nomeAcervoExibirDTO}}</td><td md-cell><md-checkbox id=checkBoxPrincipal{{$index}} ng-model=arquivoAcervo.icPrincipalDTO class=md-primary disabled></td><td md-cell layout-align=\"center center\" style=\"width: 20%\"><md-icon id=iconeEditarAcervo ng-click=editar(arquivoAcervo.idArquivoAcervoDTO) class=space-icon>mode_edit</md-icon><md-icon ng-click=visualizar(arquivoAcervo.idArquivoAcervoDTO) class=space-icon>search</md-icon></td></tr></tbody></table></md-table-container></div></div><div style=margin-top:30px><span>* O protocolo consultado está associado a todos os documentos principais apresentaods.</span></div></fieldset></div>");
$templateCache.put("scripts-acervo/app/cadastro-documento/view/arquivo-anexo.tmpl.html","<div class=acervo-cadastro-documento><md-tabs md-selected=selectedTabIndex md-dynamic-height md-border-bottom><md-tab label=\"Descritores Informacionais\" ng-click=goDescritoresInformacionais() ng-disabled=desabilitarAbaDescritorInformacional></md-tab><md-tab label=\"Descritores Catalográficos\" ng-click=goDescritoresCatalograficos() ng-disabled=desabilitarAbaDescritorCatalografico></md-tab><md-tab label=\"Descritores Técnicos\" ng-click=previous() ng-disabled=desabilitarDescritorTecnico></md-tab><md-tab label=\"Anexar Arquivos\"><ng-form name=form><fieldset flex=100 style=min-width:0><legend>Anexar Arquivo</legend><div layout=column flex=100 ng-repeat=\"(indiceX, tabela) in listaTabelas track by indiceX\" style=margin-bottom:70px><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=column-width-min>Tipo</th><th md-column class=column-width-max>Nome</th><th md-column class=column-width-min>Formato</th><th md-column class=column-width-min>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"(indiceY, arquivoAnexo) in tabela track by indiceY\"><td md-cell>{{arquivoAnexo.dsTipo}}</td><td md-cell>{{arquivoAnexo.dsNome}}</td><td md-cell>{{arquivoAnexo.dsFormato}}</td><td md-cell><ng-md-icon icon=add size=19 alt=INCLUIR class=space-icon ng-show=arquivoAnexo.mostrarIncluir ng-click=\"incluirNovoArquivo(arquivoAnexo, indiceX)\"></ng-md-icon><ng-md-icon icon=mode_edit size=19 alt=EDITAR class=space-icon ng-show=arquivoAnexo.mostrarEditar ng-click=\"alterarNovoArquivo(arquivoAnexo, indiceX)\"></ng-md-icon><ng-md-icon icon=search size=19 alt=VISUALIZAR class=space-icon ng-show=arquivoAnexo.mostrarVisualizar ng-click=\"donwloadArquivoAnexo(arquivoAnexo.idArquivoAnexo, indiceX)\"></ng-md-icon><ng-md-icon icon=delete size=19 alt=EXCLUIR class=space-icon ng-show=arquivoAnexo.mostrarDeletar ng-click=\"deletarArquivoAnexo(arquivoAnexo.idArquivoAnexo, indiceX)\"></ng-md-icon></td></tr></tbody></table></md-table-container></div><div style=\"text-align: right; margin-top:30px\"><md-button id=\"botaoAdd{{name + $index}}\" class=md-raised ng-click=add() ng-disabled=\"bloquearTodosCampos || !isArquivoAnteriorNaoPreenchido()\"><md-tooltip ng-show=!isArquivoAnteriorNaoPreenchido() md-direction=bottom>Anexo anterior não adicionado</md-tooltip><md-icon md-font-set=material-icons>add</md-icon>Adicionar outra parte do documento</md-button></div></fieldset><botoes-cadastro previous=previous() cancelar=goConsultar() save=\"\" next=next() show-previous=true show-next=!bloquearTodosCampos show-save=false></botoes-cadastro></ng-form></md-tab><md-tab label=Histórico ng-click=goHistorico() ng-disabled=desabilitarAbaHitorico></md-tab></md-tabs></div>");
$templateCache.put("scripts-acervo/app/cadastro-documento/view/descritores-catalograficos.tmpl.html","<div class=acervo-cadastro-documento><md-tabs md-selected=selectedTabIndex md-dynamic-height md-border-bottom><md-tab label=\"Descritores Informacionais\" ng-click=previous() ng-disabled=desabilitarAbaDescritorInformacional></md-tab><md-tab label=\"Descritores Catalográficos\"><ng-form name=form><fieldset ng-if=!isTipoIconografico()><legend>Cadastro dos Descritores Catalográficos</legend><div layout=column flex=100><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=30><label>Estante</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoDTO.dsEstanteDTO ng-disabled=bloquearTodosCampos></md-input-container><md-input-container flex=30><label>Prateleira</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoDTO.dsPrateleiraDTO ng-disabled=bloquearTodosCampos></md-input-container><md-input-container flex=30><label>Caixa</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoDTO.dsCaixaDTO required ng-disabled=bloquearTodosCampos><div ng-messages=form.arquivoAcervoDTO.descritorCatalograficoDTO.dsCaixaDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row class=gray-text-color layout-align=\"center none\" flex=100 style=\"min-height: 70px\" ng-if=isTipoProcesso()><div flex=90 ng-class=\"(form.$submitted == true &&\n              arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO == null) ? \'invalid-select\' : \'\'\"><p>Notação Kardex *</p><md-radio-group layout=row ng-model=arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO ng-required=!arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO ng-disabled=bloquearTodosCampos><md-radio-button id=radioKardexSim ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button id=radioKardexNao ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div></div><div layout=row ng-if=arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO flex=100 layout-align=\"center center\"><div layout=row flex=90 layout-align=\"center center\"><md-input-container flex=30><label>Armário</label><input ng-model=kardexModel.dsArmarioDTO ng-required=\"arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO &&\n                            arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO.length === 0\" ng-disabled=bloquearTodosCampos></md-input-container><md-input-container flex=30><label>Gaveta</label><input ng-model=kardexModel.dsGavetaDTO ng-required=\"arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO &&\n                            arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO.length === 0\" ng-disabled=bloquearTodosCampos></md-input-container><md-input-container flex=30><label>Maço</label><input ng-model=kardexModel.dsMacoDTO ng-required=\"arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO &&\n                            arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO.length === 0\" ng-disabled=bloquearTodosCampos></md-input-container><div flex=10 layout-align=\"center center\"><md-button id=botaoAddKardex class=\"md-fab md-mini multi-composite-field-add\" ng-click=addKardex() ng-disabled=bloquearTodosCampos><md-icon>add</md-icon></md-button></div></div></div><div layout=row layout-align=\"center none\" ng-if=arquivoAcervoDTO.descritorCatalograficoDTO.icNotacaoKardexDTO><div flex=90 style=\"padding-bottom: 40px\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column>Armário</th><th md-column>Gaveta</th><th md-column>Maço</th><th md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"kardex in arquivoAcervoDTO.descritorCatalograficoDTO.listKardexDTO\"><td md-cell>{{kardex.dsArmarioDTO}}</td><td md-cell>{{kardex.dsGavetaDTO}}</td><td md-cell>{{kardex.dsMacoDTO}}</td><td md-cell layout-align=\"center center\" ng-disabled=bloquearTodosCampos><md-icon ng-click=removeKardex($index)>delete</md-icon></td></tr></tbody></table></md-table-container></div></div><div layout=row layout-align=\"center none\" flex=100 style=\"min-height: 90px\" ng-if=isTipoProcesso()><md-input-container flex=90><label>Dimensão do Suporte</label><md-select ng-model=arquivoAcervoDTO.descritorCatalograficoDTO.dimensaoSuporteDTO ng-model-options=\"{trackBy: \'$value.idDimensaoSuporteDTO\'}\" ng-disabled=bloquearTodosCampos required><md-option id=opcaoDimensao{{$index}} ng-repeat=\"dimensaoSuporte in dimensoesSuporte\" ng-value=dimensaoSuporte>{{dimensaoSuporte.dsDimensaoSuporteDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritorCatalograficoDTO.dimensaoSuporteDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 style=\"min-height: 90px\" ng-if=isTipoProcesso()><md-input-container flex=90><label>Condição do Original</label><md-select ng-model=arquivoAcervoDTO.descritorCatalograficoDTO.condicaoOriginalDTO ng-model-options=\"{trackBy: \'$value.idCondicaoOriginalDTO\'}\" ng-disabled=bloquearTodosCampos required><md-option id=opcaoCondicaoOriginal{{$index}} ng-repeat=\"condicaoOriginal in condicoesOriginal\" ng-value=condicaoOriginal>{{condicaoOriginal.dsCondicaoOriginalDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritorCatalograficoDTO.condicaoOriginalDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 style=\"min-height: 70px\" ng-if=isTipoProcesso()><div flex=90 class=gray-text-color><p>Iconografia</p><md-radio-group layout=row ng-model=arquivoAcervoDTO.descritorCatalograficoDTO.icIconografiaDTO ng-disabled=bloquearTodosCampos><md-radio-button id=radioIconografiaSim ng-value=iconografiaSim class=md-primary>Sim</md-radio-button><md-radio-button id=radioIconografiaNao ng-value=iconografiaNao class=md-primary>Não</md-radio-button></md-radio-group></div></div><div layout=row layout-align=\"center none\" flex=100 style=\"min-height: 90px\" ng-if=\"arquivoAcervoDTO.descritorCatalograficoDTO.icIconografiaDTO === iconografiaSim\"><md-input-container flex=90 id=especies><p style=\"margin-bottom: 10px\">Espécie</p><div flex=100 ng-repeat=\"especie in especies\" id=especie{{$index}}><md-checkbox class=md-primary ng-disabled=bloquearTodosCampos ng-checked=\"exists(especie, selected)\" ng-click=\"toggle(especie, selected)\">{{ especie.dsEspecieDTO }}</md-checkbox></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 style=\"margin-bottom: 100px\"><md-input-container class=md-block flex=90><label>Informações Complementares</label><textarea ng-model=arquivoAcervoDTO.descritorCatalograficoDTO.txInformacoesComplementaresDTO md-maxlength=2000 rows=5 md-select-on-focus ng-disabled=bloquearTodosCampos></textarea></md-input-container></div></div></fieldset><fieldset ng-if=isTipoIconografico()><legend>Cadastro dos Descritores Catalográficos</legend><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Item</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsItemDTO ng-disabled=bloquearTodosCampos required numbers-only><div ng-messages=form.arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsItemDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Mapoteca</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsMapotecaDTO ng-disabled=bloquearTodosCampos required numbers-only><div ng-messages=form.arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsMapotecaDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Gaveta</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsGavetaDTO ng-disabled=bloquearTodosCampos required numbers-only><div ng-messages=form.arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsGavetaDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Prancha</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsPranchaDTO ng-disabled=bloquearTodosCampos numbers-only></md-input-container></div><fieldset><legend>Notação Antiga</legend><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Planta</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsPlantaDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Armário</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsArmarioDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Tela</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsTelaDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Tubo</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsTuboDTO ng-disabled=bloquearTodosCampos></md-input-container></div></fieldset><div layout=row layout-align=\"center none\" flex=100 style=\"min-height: 90px\"><md-input-container flex=90><label>Documento</label><md-select ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.documentoDTO ng-model-options=\"{trackBy: \'$value.idDocumentoDTO\'}\" ng-disabled=bloquearTodosCampos required><md-option ng-repeat=\"documento in documentos\" ng-value=documento>{{documento.dsDocumentoDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.documentoDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Órgão de Origem</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsOrgaoOrigemDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Órgao de Tutela</label><input ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsOrgaoTutelaDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 style=\"min-height: 90px\"><md-input-container flex=90><label>Material do Suporte</label><md-select ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.materialSuporteDTO ng-model-options=\"{trackBy: \'$value.idMaterialSuporteDTO\'}\" ng-disabled=bloquearTodosCampos required><md-option ng-repeat=\"materialSuporte in materiaisSuporte\" ng-value=materialSuporte>{{materialSuporte.dsMaterialSuporteDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.materialSuporteDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 style=\"min-height: 90px\"><md-input-container flex=90><label>Dimensão do Suporte</label><md-select ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dimensaoSuporteDTO ng-model-options=\"{trackBy: \'$value.idDimensaoSuporteDTO\'}\" ng-disabled=bloquearTodosCampos required><md-option ng-repeat=\"dimensaoSuporte in dimensoesSuporte\" ng-value=dimensaoSuporte>{{dimensaoSuporte.dsDimensaoSuporteDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dimensaoSuporteDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 style=\"min-height: 90px\"><md-input-container flex=90><label>Condição do Original</label><md-select ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.condicaoOriginalDTO ng-model-options=\"{trackBy: \'$value.idCondicaoOriginalDTO\'}\" ng-disabled=bloquearTodosCampos required><md-option ng-repeat=\"condicaoOriginal in condicoesOriginal\" ng-value=condicaoOriginal>{{condicaoOriginal.dsCondicaoOriginalDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.condicaoOriginalDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container class=md-block flex=90><label>Informações Complementares</label><textarea ng-model=arquivoAcervoDTO.descritorCatalograficoIconograficoDTO.dsInfoComplementaresDTO md-maxlength=2000 maxlength=2000 rows=5 md-select-on-focus ng-disabled=bloquearTodosCampos></textarea></md-input-container></div></fieldset><botoes-cadastro previous=previous() cancelar=goConsultar() save=save(form) next=\"save(form, true)\" show-previous=true show-next=true show-save=!bloquearTodosCampos></botoes-cadastro></ng-form></md-tab><md-tab label=\"Descritores Técnicos\" ng-click=next() ng-disabled=desabilitarAbaDescritorTecnico></md-tab><md-tab label=\"Anexar Arquivos\" ng-click=goArquivoAcervo() ng-disabled=desabilitarAbaAnexarArquivo></md-tab><md-tab label=Histórico ng-click=goHistorico() ng-disabled=desabilitarAbaHitorico></md-tab></md-tabs></div>");
$templateCache.put("scripts-acervo/app/cadastro-documento/view/descritores-informacionais.tmpl.html","<div class=acervo-cadastro-documento><md-tabs md-selected=selectedTabIndex md-dynamic-height md-border-bottom><md-tab label=\"Descritores Informacionais\"><ng-form name=form><fieldset><legend>Acervo Documental</legend><div layout=row layout-align=\"space-around none\" style=\"height: 80px\"><md-input-container flex=40><label>Categoria</label><md-select ng-model=arquivoAcervoDTO.categoriaDTO ng-change=onChangeCategoria() ng-model-options=\"{trackBy: \'$value.idCategoriaDTO\'}\" ng-disabled=bloquearTodosCampos required><md-option ng-repeat=\"categoria in categorias\" ng-value=categoria>{{categoria.dsCategoriaDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.categoriaDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container><md-input-container flex=40 ng-if=isTextual><label>Espécie Documental</label><md-select ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.especieDocumentalDTO ng-model-options=\"{trackBy: \'$value.idEspecieDocumentalDTO\'}\" ng-disabled=bloquearTodosCampos required><md-option ng-repeat=\"especieDocumental in especieDocumentais\" ng-value=especieDocumental>{{especieDocumental.dsEspecieDocumentalDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritoresInformacionaisDTO.especieDocumentalDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div></fieldset><fieldset ng-if=\"arquivoAcervoDTO.descritoresInformacionaisDTO.especieDocumentalDTO && isTextual\"><legend>Cadastro dos Descritores Informacionais</legend><div layout=row layout-align=\"center none\" class=processos><div layout=column flex=100><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Processo Administrativo</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsProcessoAdmnistrativoDTO ng-disabled=bloquearTodosCampos required><div ng-messages=form.arquivoAcervoDTO.descritoresInformacionaisDTO.dsProcessoAdmnistrativoDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Assunto</label><md-select ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.assuntoDTO ng-model-options=\"{trackBy: \'$value.coAssuntoDTO\'}\" ng-disabled=bloquearTodosCampos required><md-select-header><input data-ng-model=termo type=search placeholder=\"Buscar assunto\" ng-keydown=searchAssunto($event) style=\"width: 100%; border-style: none none solid; border-bottom: 1px solid gray\"></md-select-header><md-option ng-repeat=\"assunto in assuntos | filter:termo\" ng-value=assunto>{{assunto.coAssuntoDTO + \' \' + assunto.dsAssuntoDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritoresInformacionaisDTO.assuntoDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Título Formal</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsTituloFormalDTO ng-disabled=bloquearTodosCampos required><div ng-messages=form.arquivoAcervoDTO.descritoresInformacionaisDTO.dsTituloFormalDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Órgão Emissor</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsOrgaoEmissorDTO ng-disabled=bloquearTodosCampos required><div ng-messages=form.arquivoAcervoDTO.descritoresInformacionaisDTO.dsOrgaoEmissorDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><multi-composite-field id=interessadosCampoData model=arquivoAcervoDTO.descritoresInformacionaisDTO.listInteressadosDTO label=\"\'Interessados\'\" flex=90 name=\"\'dsInteressadosDTO\'\" ng-if=isProcesso() disabled obrigatorio=true></multi-composite-field><div layout=row layout-align=\"center none\" flex=100 ng-if=isFichaFinanceira()><md-input-container flex=40><label>RIP SPIU</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.nuRipSpiuDTO ng-disabled=bloquearTodosCampos numbers-only></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isFichaFinanceira()><md-input-container flex=40><label>RIP SIAPA</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.nuRipSiapaDTO ng-disabled=bloquearTodosCampos numbers-only></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Logradouro Original</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsLogradouroOriginalDTO ng-required=\"arquivoAcervoDTO.descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 2 || !arquivoAcervoDTO.descritoresInformacionaisDTO.dsConfrontacoesDTO\" ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Confrontações</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsConfrontacoesDTO ng-disabled=bloquearTodosCampos ng-required=!arquivoAcervoDTO.descritoresInformacionaisDTO.dsLogradouroOriginalDTO></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Logradouro Atualizado</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsLogradouroAtualizadoDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Unidade Federativa</label><md-select ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.unidadeFederativaDTO ng-model-options=\"{trackBy: \'$value.idUnidadeFederativaDTO\'}\" ng-disabled=bloquearTodosCampos><md-option ng-repeat=\"unidadeFederativa in unidadesFederativas\" ng-value=unidadeFederativa ng-click=onChangeUnidadeFederativa(unidadeFederativa.idUnidadeFederativaDTO)>{{unidadeFederativa.dsUnidadeFederativaDTO}}</md-option></md-select></md-input-container></div><div layout=row layout-align=\"space-around none\" flex=100 ng-if=isProcesso()><md-input-container flex=40 ng-if=!arquivoAcervoDTO.descritoresInformacionaisDTO.icMunicipioNaoExistenteDTO><label>Município</label><md-select ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.municipioDTO ng-model-options=\"{trackBy: \'$value.idMunicipioDTO\'}\" ng-disabled=\"!municipios.length && !arquivoAcervoDTO.descritoresInformacionaisDTO.municipioDTO || bloquearTodosCampos\"><md-option ng-repeat=\"municipio in municipios\" ng-value=municipio>{{municipio.dsMunicipioDTO}}</md-option></md-select></md-input-container><md-input-container flex=40 ng-if=arquivoAcervoDTO.descritoresInformacionaisDTO.icMunicipioNaoExistenteDTO><label>Município</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.noMunicipioNaoExistenteDTO ng-disabled=bloquearTodosCampos ng-required=arquivoAcervoDTO.descritoresInformacionaisDTO.icMunicipioNaoExistenteDTO><div ng-messages=form.arquivoAcervoDTO.descritoresInformacionaisDTO.noMunicipioNaoExistenteDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container><md-input-container flex=40><md-checkbox ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.icMunicipioNaoExistenteDTO class=md-primary ng-disabled=bloquearTodosCampos ng-change=\"onChangeMunicipioNaoExiste(arquivoAcervoDTO.descritoresInformacionaisDTO.icMunicipioNaoExistenteDTO, 1)\"><p>Não existe mais</p></md-checkbox></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Bairro</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsBairroDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"space-around none\" flex=100><md-input-container flex=40><label>CEP</label><input ui-br-cep-mask ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsCepDTO ng-disabled=bloquearTodosCampos></md-input-container><md-input-container flex=40><md-button class=md-raised style=\"text-transform:none !important\" ng-click=buscarCep(arquivoAcervoDTO.descritoresInformacionaisDTO.dsCepDTO)>Buscar e preencher endereço</md-button></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Tipo do Patrimônio</label><md-select name=tipoPatrimonio ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.patrimonioDTO ng-model-options=\"{trackBy: \'$value.dsPatrimonioDTO\'}\" ng-disabled=bloquearTodosCampos><md-option></md-option><md-option ng-repeat=\"patrimonio in patrimonios\" ng-value=patrimonio>{{patrimonio.dsPatrimonioDTO}}</md-option></md-select></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Denominação Patrimonial</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsDenominacaoPatrimonialDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"space-around none\" flex=100 style=\"margin-left: 2%\" ng-if=isProcesso()><partial-date-field model=arquivoAcervoDTO.descritoresInformacionaisDTO.dtAtuacaoProcessoDTO label=\"\'Data de Autuação do Processo (dd/mm/aaaa)\'\" obrigatorio=!isDataNaoConsta id=\"\'processoAtuacaoData\'\" flex=90 ng-if=isProcesso() desabilitado=\"isDataNaoConsta || bloquearTodosCampos\"></partial-date-field><md-input-container flex=40><md-checkbox ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.icNaoConstaDataDTO class=md-primary ng-disabled=bloquearTodosCampos ng-change=onChangeDataConsta()><p>N/C (não consta)</p></md-checkbox></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Pasta de Registro de Referência</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsPastaRequisitoRefDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Número SEI</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.nuSeiDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso() style=\"clear: both\"><md-chips ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.listProcessoAntecedenteDTO md-autocomplete-snap md-transform-chip=transformChipProcessoAntecedente($chip) md-require-match=false flex=90 readonly><md-autocomplete md-selected-item=selectedItem md-search-text=searchText md-items=\"item in querySearch(ctrl.searchText)\" md-item-text=item.dsProcessoAntecedenteDTO placeholder=\"Processos Antecedentes\"><span md-highlight-text=searchText></span></md-autocomplete><md-chip-template><span><strong>{{$chip.dsProcessoAntecedenteDTO}}</strong></span></md-chip-template></md-chips></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>Processo de Outro Órgão</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsProcessoOutroOrgaoDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso()><md-input-container flex=90><label>RIP</label><input ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.nuRipDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 style=\"display: block;margin-left: 5%\"><md-input-container class=md-block flex=90><label>Informações Complementares</label><textarea ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.dsInfoComplementaresDTO md-maxlength=2000 maxlength=2000 rows=5 md-select-on-focus ng-disabled=bloquearTodosCampos style=\"max-height: 25vh; overflow-y: auto\"></textarea></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 ng-if=isProcesso() style=\"clear: both\"><md-chips ng-model=arquivoAcervoDTO.descritoresInformacionaisDTO.palavraChavesDTO md-autocomplete-snap md-transform-chip=transformChip($chip) md-require-match=false flex=90 readonly><md-autocomplete md-selected-item=selectedItem md-search-text=searchText md-items=\"item in querySearch(ctrl.searchText)\" md-item-text=item.dsPalavraChaveDTO placeholder=Palavras-Chave><span md-highlight-text=searchText></span></md-autocomplete><md-chip-template><span><strong>{{$chip.dsPalavraChaveDTO}}</strong></span></md-chip-template></md-chips></div></div></div></fieldset><fieldset ng-if=isIconografico><legend>Cadastro dos Descritores Informacionais</legend><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Título do Documento</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsTipoDocumentoDTO ng-disabled=bloquearTodosCampos required><div ng-messages=form.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsTipoDocumentoDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Unidade Federativa</label><md-select ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.unidadeFederativaDTO ng-model-options=\"{trackBy: \'$value.idUnidadeFederativaDTO\'}\" ng-disabled=bloquearTodosCampos required><md-option ng-repeat=\"unidadeFederativa in unidadesFederativas\" ng-value=unidadeFederativa ng-click=onChangeUnidadeFederativa(unidadeFederativa.idUnidadeFederativaDTO)>{{unidadeFederativa.dsUnidadeFederativaDTO}}</md-option></md-select></md-input-container></div><div layout=row layout-align=\"space-around none\" style=\"height: 80px\"><md-input-container flex=40 ng-if=!arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.icMunicipioNaoExistenteDTO><label>Município</label><md-select ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.municipioDTO ng-model-options=\"{trackBy: \'$value.idMunicipioDTO\'}\" ng-disabled=\"!municipios.length && !arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.municipioDTO || bloquearTodosCampos\"><md-option ng-repeat=\"municipio in municipios\" ng-value=municipio>{{municipio.dsMunicipioDTO}}</md-option></md-select></md-input-container><md-input-container flex=40 ng-if=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.icMunicipioNaoExistenteDTO><label>Município</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.noMunicipioNaoExistenteDTO ng-disabled=bloquearTodosCampos ng-required=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.icMunicipioNaoExistenteDTO><div ng-messages=form.arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.noMunicipioNaoExistenteDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container><md-input-container flex=40><md-checkbox ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.icMunicipioNaoExistenteDTO class=md-primary ng-disabled=bloquearTodosCampos ng-change=\"onChangeMunicipioNaoExiste(arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.icMunicipioNaoExistenteDTO, 2)\"><p>Não existe mais</p></md-checkbox></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Região Administrativa</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsRegiaoAdministrativaDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Bairro</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsBairroDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Logradouro Original</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsLogradouroOriginalIconograficoDTO ng-disabled=bloquearTodosCampos ng-required=!arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsConfrontacoesIconograficoDTO></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Confrontações</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsConfrontacoesIconograficoDTO ng-disabled=bloquearTodosCampos ng-required=!arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsLogradouroOriginalIconograficoDTO></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Logradouro Atualizado</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsLogradouroAtualizadoIconograficoDTO ng-disabled=bloquearTodosCampos></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>RIP</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.nuRipDTO ng-disabled=bloquearTodosCampos numbers-only></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Tipo Documental</label><md-select ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.tipoDocumentalDTO ng-model-options=\"{trackBy: \'$value.idTipoDucumentalDTO\'}\" ng-disabled=bloquearTodosCampos><md-option ng-repeat=\"tipoDocumental in tiposDocumentais\" ng-value=tipoDocumental>{{tipoDocumental.dsTipoDocumentalDTO}}</md-option></md-select></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Vinculado a Processos SPU?</label><br><br><md-radio-group ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.icVinculadoSpuDTO layout=row ng-disabled=bloquearTodosCampos><md-radio-button id=radioVinculadoIcoSim ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button id=radioVinculadoIcoNao ng-value=false>Não</md-radio-button></md-radio-group></md-input-container></div><br><br><div layout=row layout-align=\"center none\" flex=100 ng-if=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.icVinculadoSpuDTO style=\"clear: both\"><md-chips ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listProcessoVinculadoDTO md-autocomplete-snap md-transform-chip=transformChipProcessoVinculado($chip) md-require-match=false flex=90 readonly><md-autocomplete md-selected-item=selectedItem md-search-text=searchText md-items=\"item in querySearch(ctrl.searchText)\" md-item-text=item.dsProcessoVinculadoDTO placeholder=\"Processos Vinculados\"><span md-highlight-text=searchText></span></md-autocomplete><md-chip-template><span><strong>{{$chip.dsProcessoVinculadoDTO}}</strong></span></md-chip-template></md-chips></div><br><br><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Órgão Emissor</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsOrgaoEmissorDTO ng-disabled=bloquearTodosCampos></md-input-container></div><br><br><multi-composite-field model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listAutorOriginalDTO label=\"\'Autor do Original\'\" flex=90 name=\"\'dsAutorOriginalDTO\'\" disabled ng-if=true obrigatorio=false></multi-composite-field><br><br><partial-date-field model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dtOriginalDTO label=\"\'Data do Original\'\" obrigatorio=false id=\"\'originalData\'\" flex=90 desabilitado=bloquearTodosCampos></partial-date-field><multi-composite-field model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listAutorCopiaDTO label=\"\'Autor da Cópia\'\" flex=90 name=\"\'dsAutorCopiaDTO\'\" disabled ng-if=true obrigatorio=false></multi-composite-field><br><br><partial-date-field model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dtCopiaDTO label=\"\'Data da Cópia\'\" obrigatorio=false id=\"\'copiaData\'\" flex=90 desabilitado=bloquearTodosCampos></partial-date-field><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Escala Cartográfica</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.nuEscalaCartograficaDTO ng-disabled=bloquearTodosCampos ui-mask=?9:?9?9?9?9?9?9?9?9?9?9 ui-mask-placeholder ui-mask-placeholder-char=space></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Área Total</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.nuAreaTotalDTO ng-disabled=bloquearTodosCampos numbers-only ng-change=gerarAreaEquivalente()></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Unidade de Medida</label><md-select ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.unidadeMedidaDTO ng-model-options=\"{trackBy: \'$value.idUnidadeMedidaDTO\'}\" ng-disabled=bloquearTodosCampos ng-change=gerarAreaEquivalente()><md-option ng-repeat=\"unidadeMedida in unidadesMedida\" ng-value=unidadeMedida>{{unidadeMedida.dsUnidadeMedidaDTO}}</md-option></md-select></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Área Equivalente (m²)</label><input ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.nuAreaEquivalenteDTO ng-disabled=true numbers-only></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Agrupamento Temático</label><md-select ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.agrupamentoTematicoDTO ng-model-options=\"{trackBy: \'$value.idAgrupamentoTematicoDTO\'}\" ng-disabled=bloquearTodosCampos><md-option ng-repeat=\"agrupamentoTematico in agrupamentosTematicos\" ng-value=agrupamentoTematico>{{agrupamentoTematico.dsAgrupamentoTematicoDTO}}</md-option></md-select></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 style=\"display: block;margin-left: 5%\"><md-input-container class=md-block flex=90><label>Informações Complementares</label><textarea ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.dsInformacoesComplementaresDTO md-maxlength=2000 maxlength=2000 rows=5 md-select-on-focus ng-disabled=bloquearTodosCampos style=\"max-height: 25vh; overflow-y: auto\"></textarea></md-input-container></div><div layout=row layout-align=\"center none\" flex=100 style=\"clear: both\"><md-chips ng-model=arquivoAcervoDTO.descritoesInformacionaisIconograficoDTO.listPalavraChaveDTO md-autocomplete-snap md-transform-chip=transformChip($chip) md-require-match=false flex=90 ng-disabled=bloquearTodosCampos><md-autocomplete md-selected-item=selectedItem md-search-text=searchText md-items=\"item in querySearch(ctrl.searchText)\" md-item-text=item.dsPalavraChaveDTO placeholder=Palavras-Chave><span md-highlight-text=searchText></span></md-autocomplete><md-chip-template><span><strong>{{$chip.dsPalavraChaveDTO}}</strong></span></md-chip-template></md-chips></div><br><br></fieldset><botoes-cadastro previous=null cancelar=goConsultar() save=save(form) next=\"save(form, true)\" show-previous=false show-next=true show-save=!bloquearTodosCampos></botoes-cadastro></ng-form></md-tab><md-tab label=\"Descritores Catalográficos\" ng-click=next() ng-disabled=desabilitarAbaDescritorCatalografico></md-tab><md-tab label=\"Descritores Técnicos\" ng-click=goDescritorTecnico() ng-disabled=desabilitarAbaDescritorTecnico></md-tab><md-tab label=\"Anexar Arquivos\" ng-click=goArquivoAcervo() ng-disabled=desabilitarAbaAnexarArquivo></md-tab><md-tab label=Histórico ng-click=goHistorico() ng-disabled=desabilitarAbaHitorico></md-tab></md-tabs></div>");
$templateCache.put("scripts-acervo/app/cadastro-documento/view/descritores-tecnicos.tmpl.html","<div class=acervo-cadastro-documento><md-tabs md-selected=selectedTabIndex md-dynamic-height md-border-bottom><md-tab label=\"Descritores Informacionais\" ng-click=goDescritoresInformacionais() ng-disabled=desabilitarAbaDescritorInformacional></md-tab><md-tab label=\"Descritores Catalográficos\" ng-click=previous() ng-disabled=desabilitarAbaDescritorCatalografico><md-tab label=\"Descritores Técnicos\"><ng-form name=form ng-if=isArquivoAcervoCarregado()><fieldset class=input-container-fix><legend>Cadastro dos Descritores Técnicos</legend><div layout=column flex=100><div layout-wrap layout-gt-sm=row><div flex-gt-sm=50><md-checkbox ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.icTiffDTO aria-label=\"Checkbox 1\" ng-change=selecionarMatrizTIFF(form) class=md-primary ng-disabled=bloquearTodosCampos>Possui Matriz .TIFF?</md-checkbox></div></div><fieldset><legend>Matriz</legend><br><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Data de Produção da Matriz do Representante Digital (dd/mm/aaaa)</label><md-datepicker ng-model=tela.dtProdMatrizDTO ng-required=!isMatrizBloqueada() ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\" md-placeholder=Data md-open-on-focus ng-if=true></md-datepicker></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Tipo de Equipamento Utilizado</label><md-select ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.equipamentoUtilizadoDTO ng-model-options=\"{trackBy: \'$value.idEquipamentoUtilizado\'}\" ng-required=!isMatrizBloqueada() ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\"><md-option ng-repeat=\"equipamento in equipamentosUtilizados\" ng-value=equipamento>{{equipamento.dsEquipamentoUtilizado}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.equipamentoUtilizadoDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><br><br><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Dimensão da Imagem em Relação ao Original</label><md-select ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.dimensaoImgRelacaoDTO ng-model-options=\"{trackBy: \'$value.idDimensaoImgRelacao\'}\" ng-required=!isMatrizBloqueada() ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\"><md-option ng-repeat=\"dimensao in dimensoesImg\" ng-value=dimensao>{{dimensao.dsDimensaoImgRelacao}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.dimensaoImgRelacaoDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><br><br><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Modo Cores</label><md-select ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.modoCoresDTO ng-model-options=\"{trackBy: \'$value.idModoCoresDTO\'}\" ng-required=!isMatrizBloqueada() ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\"><md-option ng-repeat=\"cores in modoCores\" ng-value=cores>{{cores.dsModoCoresDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.modoCoresDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><br><br><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Resolução Linear Adotada</label><md-select ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.resolucaoLinearDTO ng-model-options=\"{trackBy: \'$value.idResolucaoLinearDTO\'}\" ng-required=!isMatrizBloqueada() ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\"><md-option ng-repeat=\"resolucaoLinear in resolucoesLinear\" ng-value=resolucaoLinear>{{resolucaoLinear.dsResolucaoLinearDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.resolucaoLinearDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><br><br><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Resolução Tonal</label><md-select ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.resolucaoTonalDTO ng-model-options=\"{trackBy: \'$value.idResolucaoTonalDTO\'}\" ng-required=!isMatrizBloqueada() ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\"><md-option ng-repeat=\"resolucaoTonal in resolucoesTonal\" ng-value=resolucaoTonal>{{resolucaoTonal.dsResolucaoTonalDTO}}</md-option></md-select><div ng-messages=form.arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.resolucaoTonalDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><br><br><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Houve Processo de Interpolação?</label><br><br><md-radio-group ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.icProcessoInterpolacaoDTO layout=row ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\"><md-radio-button id=opcaoSimProcessoInterolacao ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button id=opcaoNaoProcessoInterolacao ng-value=false>Não</md-radio-button></md-radio-group><div ng-messages=form.arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.icProcessoInterpolacaoDTO.$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container></div><br><br><br><br><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Software de Captura</label><input id=inputSoftCaptura ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.dsSoftwareCapturaDTO ng-required=!isMatrizBloqueada() ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\"></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Software de Processamento de Imagem</label><input id=inputSoftProcessamentoImg ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.dsSoftwareProcImgDTO ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\"></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Sistema Operacional Utiilizado</label><input id=inputSistemaOperacionalUtilizado ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.dsSistemaOpDTO ng-required=!isMatrizBloqueada() ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\"></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Tamanho do Arquivo em KB</label><input id=inputTamArquivo ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.nuTamArquivoDTO ng-required=!isMatrizBloqueada() ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\" numbers-only></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Responsável pela Produção da Matriz do Responsável Digital</label><input id=inputRespProducaoMatriz ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoTiffDTO.dsRespProdMatrizDTO ng-required=!isMatrizBloqueada() ng-disabled=\"isMatrizBloqueada() || bloquearTodosCampos\"></md-input-container></div></fieldset><br><div layout-wrap layout-gt-sm=row><div flex-gt-sm=50><md-checkbox ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.icPdfDTO aria-label=\"Checkbox 1\" ng-change=selecionarDerivadaPDF() ng-disabled=\"isDerivadasBloqueadas() || bloquearTodosCampos\" class=md-primary>Possui Derivada .PDF?</md-checkbox></div></div><fieldset><legend>Derivada PDF</legend><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Data de Produção das Derivadas</label><md-datepicker ng-model=tela.dtProdPdfDTO ng-required=\"!isDerivadasBloqueadasPDF() && !isDerivadasBloqueadas()\" ng-disabled=\"isDerivadasBloqueadasPDF() || isDerivadasBloqueadas() || bloquearTodosCampos\" md-placeholder=Data md-open-on-focus></md-datepicker></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Software de Geração das Derivas</label><input ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO.dsSoftGerPdfDTO ng-required=\"!isDerivadasBloqueadasPDF() && !isDerivadasBloqueadas()\" ng-disabled=\"isDerivadasBloqueadasPDF() || isDerivadasBloqueadas() || bloquearTodosCampos\"></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Responsável pela Produção das Derivadas</label><input ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoPdfDTO.dsRespProdPdfDTO ng-required=\"!isDerivadasBloqueadasPDF() && !isDerivadasBloqueadas()\" ng-disabled=\"isDerivadasBloqueadasPDF() || isDerivadasBloqueadas() || bloquearTodosCampos\"></md-input-container></div></fieldset><br><div layout-wrap layout-gt-sm=row><div flex-gt-sm=50><md-checkbox ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.icJpegDTO aria-label=\"Checkbox 1\" ng-change=selecionarDerivadaJPEG() ng-disabled=\"isDerivadasBloqueadas() || bloquearTodosCampos\" class=md-primary>Possui Derivada .JPEG?</md-checkbox></div></div><fieldset><legend>Derivada JPEG</legend><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Data de Produção das Derivadas</label><md-datepicker ng-model=tela.dtProdJpegDTO ng-required=\"!isDerivadasBloqueadasJPEG() && !isDerivadasBloqueadas()\" ng-disabled=\"isDerivadasBloqueadasJPEG() || isDerivadasBloqueadas() || bloquearTodosCampos\" md-placeholder=Data md-open-on-focus></md-datepicker></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Software de Geração das Derivas</label><input ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO.dsSoftGerJpegDTO ng-required=\"!isDerivadasBloqueadasJPEG() && !isDerivadasBloqueadas()\" ng-disabled=\"isDerivadasBloqueadasJPEG() || isDerivadasBloqueadas() || bloquearTodosCampos\"></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Responsável pela Produção das Derivadas</label><input ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.infoJpegDTO.dsRespProdJpegDTO ng-required=\"!isDerivadasBloqueadasJPEG() && !isDerivadasBloqueadas()\" ng-disabled=\"isDerivadasBloqueadasJPEG() || isDerivadasBloqueadas() || bloquearTodosCampos\"></md-input-container></div></fieldset><br></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Data de Cadastramento</label><md-datepicker id=dataPickerCadastramento ng-model=$parent.dtCadastramentoDTO required md-placeholder=Data md-open-on-focus ng-disabled=bloquearTodosCampos></md-datepicker></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Responsável pelo Cadastramento</label><input ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.dsRespCadastramentoDTO ng-disabled=bloquearTodosCampos required></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=90><label>Informações Complementares</label><input ng-model=arquivoAcervoDTO.descritoresTecnicosDTO.dsInfoComplDTO ng-disabled=bloquearTodosCampos></md-input-container></div></fieldset><botoes-cadastro previous=previous() cancelar=goConsultar() save=save(form) next=\"save(form, true)\" show-previous=true show-next=true show-save=!bloquearTodosCampos></botoes-cadastro></ng-form></md-tab><md-tab label=\"Anexar Arquivos\" ng-click=goArquivoAcervo() ng-disabled=desabilitarAbaAnexarArquivo></md-tab><md-tab label=Histórico ng-click=goHistorico() ng-disabled=desabilitarAbaHitorico></md-tab></md-tab></md-tabs></div>");
$templateCache.put("scripts-acervo/app/cadastro-documento/view/historico-acervo.tmpl.html","<div class=acervo-cadastro-documento><md-tabs md-selected=selectedTabIndex md-dynamic-height md-border-bottom><md-tab label=\"Descritores Informacionais\" ng-click=goDescritoresInformacionais() ng-disabled=desabilitarAbaDescritorInformacional></md-tab><md-tab label=\"Descritores Catalográficos\" ng-click=goDescritoresCatalograficos() ng-disabled=desabilitarAbaDescritorCatalografico></md-tab><md-tab label=\"Descritores Técnicos\" ng-click=goDescritorTecnico() ng-disabled=desabilitarDescritorTecnico></md-tab><md-tab label=\"Anexar Arquivos\" ng-click=previous() ng-disabled=desabilitarAbaAnexarArquivo></md-tab><md-tab label=Histórico><ng-form name=form><fieldset style=min-width:0><legend>Histórico</legend><div layout=column flex=100><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=column-width-min>Data e Hora</th><th md-column class=column-width-max>Tipo de Ocorrência</th><th md-column class=column-width-min>Responsável</th><th md-column class=column-width-min>Descrição</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"historico in arquivoAcervoDTO.listHistoricoArquivoAcervoDTO\"><td md-cell>{{historico.dtHistoricoArquivoAcervoDTO | date : \"dd/MM/yyyy HH:mm:ss\"}}</td><td md-cell>{{historico.tipoHistoricoDTO.dsTipoHistoricoDTO}}</td><td md-cell>{{historico.noUsuarioDTO + \" : \" + historico.nuCpfUsuarioDTO}}</td><td md-cell><ng-md-icon icon=search size=19 alt=\"DESCRIÇÃO HISTÓRICO\" class=space-icon ng-click=abrirDescricaoHistorico(historico.idHistoricoArquivoAcervoDTO) ng-show=\"historico.tipoHistoricoDTO.idTipoHistoricoDTO != 1\n                                  && historico.tipoHistoricoDTO.idTipoHistoricoDTO != 3\n                                  && historico.tipoHistoricoDTO.idTipoHistoricoDTO != 5\n                                  && historico.tipoHistoricoDTO.idTipoHistoricoDTO != 7\n                                  && historico.tipoHistoricoDTO.idTipoHistoricoDTO != 15\n                                  && historico.tipoHistoricoDTO.idTipoHistoricoDTO != 17\"></ng-md-icon></td></tr></tbody></table></md-table-container></div></fieldset><botoes-cadastro previous=previous() cancelar=goConsultar() save=\"\" next=next() show-previous=true show-next=bloquearTodosCampos show-save=false></botoes-cadastro></ng-form></md-tab></md-tabs></div>");
$templateCache.put("scripts-acervo/app/cadastro-documento/view/modal-historico.tmpl.html","<md-dialog flex=70 aria-label=\"{{\'label-gestor\' | translate}}\" ng-cloak><md-dialog-content><div style=\"padding: 42px\"><fieldset style=min-width:0><legend>Histórico</legend><div layout=column flex=100><md-table-container><!-- Tabela de histórico inclusão arquivo anexo --><table md-table ng-show=\"resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 2\n                                          || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 4\n                                          || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 6\"><thead md-head><tr md-row><th md-column class=column-width-min>Tipo</th><th md-column class=column-width-min>Nome</th></tr></thead><tbody md-body><tr md-row><td md-cell>{{resultado[1].dsTipoArquivoDTO}}</td><td md-cell>{{resultado[1].dsArquivoAnexoDTO}}</td></tr></tbody></table><table md-table ng-show=\"resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO > 6\"><thead md-head><tr md-row><th md-column class=column-width-min>Campo</th><th md-column class=column-width-max>Atual</th><th md-column class=column-width-max>Anterior</th></tr></thead><tbody md-body><!-- Tabela de histórico publicação documento internet --><tr md-row ng-show=\"resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 16\"><td md-cell>Publicado na internet?</td><td md-cell>{{resultado[1].icPublicadoInternet | simNao}}</td><td md-cell>{{resultado[2].icPublicadoInternet | simNao}}</td></tr><!-- Tabela de histórico inclusão cadastro acervo, inclusão/alteração descritores informacionais --><tr md-row ng-show=\"resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\"><td md-cell>Categoria</td><td md-cell>{{resultado[1].categoriaDTO.dsCategoriaDTO}}</td><td md-cell>{{resultado[2].categoriaDTO.dsCategoriaDTO}}</td></tr><tr md-row ng-show=\"resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\"><td md-cell>Espécie Documental</td><td md-cell>{{resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.dsEspecieDocumentalDTO}}</td><td md-cell>{{resultado[2].descritoresInformacionaisDTO.especieDocumentalDTO.dsEspecieDocumentalDTO}}</td></tr><!-- Descritores informacionais - Campos Textual - Processos --><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Processo Administrativo</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsProcessoAdmnistrativoDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsProcessoAdmnistrativoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsProcessoAdmnistrativoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsProcessoAdmnistrativoDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Assunto</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.assuntoDTO.dsAssuntoDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].assuntoDTO.dsAssuntoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.assuntoDTO.dsAssuntoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].assuntoDTO.dsAssuntoDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Título Formal</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsTituloFormalDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsTituloFormalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsTituloFormalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsTituloFormalDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Órgão Emissor</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsOrgaoEmissorDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsOrgaoEmissorDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsOrgaoEmissorDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsOrgaoEmissorDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Interessados</td><td md-cell><div ng-show=\"resultado[1].descritoresInformacionaisDTO != null\" ng-repeat=\"interessado in resultado[1].descritoresInformacionaisDTO.listInteressadosDTO\">{{interessado.dsInteressadosDTO}}</div><div ng-show=\"resultado[1].descritoresInformacionaisDTO == null\" ng-repeat=\"interessado in resultado[1].listInteressadosDTO\">{{interessado.dsInteressadosDTO}}</div></td><td md-cell><div ng-show=\"resultado[2].descritoresInformacionaisDTO != null\" ng-repeat=\"interessado in resultado[2].descritoresInformacionaisDTO.listInteressadosDTO\">{{interessado.dsInteressadosDTO}}</div><div ng-show=\"resultado[2].descritoresInformacionaisDTO == null\" ng-repeat=\"interessado in resultado[2].listInteressadosDTO\">{{interessado.dsInteressadosDTO}}</div></td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Logradouro Original</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsLogradouroOriginalDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsLogradouroOriginalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsLogradouroOriginalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsLogradouroOriginalDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Confrontações</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsConfrontacoesDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsConfrontacoesDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsConfrontacoesDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsConfrontacoesDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Logradouro Atualizado</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsLogradouroAtualizadoDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsLogradouroAtualizadoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsLogradouroAtualizadoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsLogradouroAtualizadoDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Unidade Federativa</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.unidadeFederativaDTO.dsUnidadeFederativaDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].unidadeFederativaDTO.dsUnidadeFederativaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.unidadeFederativaDTO.dsUnidadeFederativaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].unidadeFederativaDTO.dsUnidadeFederativaDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Tipo do Patrimônio</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.patrimonioDTO.dsPatrimonioDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].patrimonioDTO.dsPatrimonioDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.patrimonioDTO.dsPatrimonioDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].patrimonioDTO.dsPatrimonioDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Denominação Patrimonial</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsDenominacaoPatrimonialDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsDenominacaoPatrimonialDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsDenominacaoPatrimonialDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsDenominacaoPatrimonialDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Pasta de Registro de Referência</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsPastaRequisitoRefDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsPastaRequisitoRefDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsPastaRequisitoRefDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsPastaRequisitoRefDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Número SEI</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.nuSeiDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].nuSeiDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.nuSeiDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].nuSeiDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Processos Antecedentes</td><td md-cell><div ng-show=\"resultado[1].descritoresInformacionaisDTO != null\" ng-repeat=\"processo in resultado[1].descritoresInformacionaisDTO.listProcessoAntecedenteDTO\">{{processo.dsProcessoAntecedenteDTO}}</div><div ng-show=\"resultado[1].descritoresInformacionaisDTO == null\" ng-repeat=\"processo in resultado[1].listProcessoAntecedenteDTO\">{{processo.dsProcessoAntecedenteDTO}}</div></td><td md-cell><div ng-show=\"resultado[2].descritoresInformacionaisDTO != null\" ng-repeat=\"processo in resultado[2].descritoresInformacionaisDTO.listProcessoAntecedenteDTO\">{{processo.dsProcessoAntecedenteDTO}}</div><div ng-show=\"resultado[2].descritoresInformacionaisDTO == null\" ng-repeat=\"processo in resultado[2].listProcessoAntecedenteDTO\">{{processo.dsProcessoAntecedenteDTO}}</div></td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Processo de Outro Órgão</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsProcessoOutroOrgaoDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsProcessoOutroOrgaoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsProcessoOutroOrgaoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsProcessoOutroOrgaoDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>RIP</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.nuRipDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].nuRipDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.nuRipDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].nuRipDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Informações Complementares</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsInfoComplementaresDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsInfoComplementaresDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsInfoComplementaresDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsInfoComplementaresDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 1))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Palavras-Chave</td><td md-cell><div ng-show=\"resultado[1].descritoresInformacionaisDTO != null\" ng-repeat=\"palavra in resultado[1].descritoresInformacionaisDTO.palavraChavesDTO\">{{palavra.dsPalavraChaveDTO}}</div><div ng-show=\"resultado[1].descritoresInformacionaisDTO == null\" ng-repeat=\"palavra in resultado[1].palavraChavesDTO\">{{palavra.dsPalavraChaveDTO}}</div></td><td md-cell><div ng-show=\"resultado[2].descritoresInformacionaisDTO != null\" ng-repeat=\"palavra in resultado[2].descritoresInformacionaisDTO.palavraChavesDTO\">{{palavra.dsPalavraChaveDTO}}</div><div ng-show=\"resultado[2].descritoresInformacionaisDTO == null\" ng-repeat=\"palavra in resultado[2].palavraChavesDTO\">{{palavra.dsPalavraChaveDTO}}</div></td></tr><!-- Descritores informacionais - Campos Textual - Fichas Financeiras --><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 2) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 2))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>RIP SPIU</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.nuRipSpiuDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].nuRipSpiuDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.nuRipSpiuDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].nuRipSpiuDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 2) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 2))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>RIP SIAPA</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.nuRipSiapaDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].nuRipSiapaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.nuRipSiapaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].nuRipSiapaDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 2) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 2))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Logradouro Original</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsLogradouroOriginalDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsLogradouroOriginalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsLogradouroOriginalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsLogradouroOriginalDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 2) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 2))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Logradouro Atualizado</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsLogradouroAtualizadoDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsLogradouroAtualizadoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsLogradouroAtualizadoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsLogradouroAtualizadoDTO}}</td></tr><tr md-row ng-show=\"((resultado[1].categoriaDTO.idCategoriaDTO == 1 && resultado[1].descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 2) || (resultado[1].especieDocumentalDTO.idEspecieDocumentalDTO == 2))\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Informações Complementares</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsInfoComplementaresDTO}}</td><td md-cell ng-show=\"resultado[1].descritoresInformacionaisDTO == null\">{{resultado[1].dsInfoComplementaresDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsInfoComplementaresDTO}}</td><td md-cell ng-show=\"resultado[2].descritoresInformacionaisDTO == null\">{{resultado[2].dsInfoComplementaresDTO}}</td></tr><!-- Descritores informacionais - Campos Iconográfico --><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Título do Documento</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.dsTipoDocumentoDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].dsTipoDocumentoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.dsTipoDocumentoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].dsTipoDocumentoDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Unidade Federativa</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.unidadeFederativaDTO.dsUnidadeFederativaDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].unidadeFederativaDTO.dsUnidadeFederativaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.unidadeFederativaDTO.dsUnidadeFederativaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].unidadeFederativaDTO.dsUnidadeFederativaDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Município</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.municipioDTO.dsMunicipioDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].municipioDTO.dsMunicipioDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.municipioDTO.dsMunicipioDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].municipioDTO.dsMunicipioDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Região Administrativa</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.dsRegiaoAdministrativaDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].dsRegiaoAdministrativaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.dsRegiaoAdministrativaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].dsRegiaoAdministrativaDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Bairro</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.dsBairroDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].dsBairroDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.dsBairroDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].dsBairroDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Logradouro Original</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.dsLogradouroOriginalDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].dsLogradouroOriginalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.dsLogradouroOriginalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].dsLogradouroOriginalDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Confrontações</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.dsConfrontacoesDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].dsConfrontacoesDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.dsConfrontacoesDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].dsConfrontacoesDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Logradouro Atualizado</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.dsLogradouroAtualizadoDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].dsLogradouroAtualizadoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.dsLogradouroAtualizadoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].dsLogradouroAtualizadoDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>RIP</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.nuRipDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].nuRipDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.nuRipDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].nuRipDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Tipo Documental</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.tipoDocumentalDTO.dsTipoDocumentalDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].tipoDocumentalDTO.dsTipoDocumentalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.tipoDocumentalDTO.dsTipoDocumentalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].tipoDocumentalDTO.dsTipoDocumentalDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Vinculado a Processos SPU?</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.icVinculadoSpuDTO | simNao}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].icVinculadoSpuDTO | simNao}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.icVinculadoSpuDTO | simNao}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].icVinculadoSpuDTO | simNao}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Processo Vinculado</td><td md-cell><div ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\" ng-repeat=\"processoVinculado in resultado[1].descritoesInformacionaisIconograficoDTO.listProcessoVinculadoDTO\">{{processoVinculado.dsProcessoVinculadoDTO}}</div><div ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\" ng-repeat=\"processoVinculado in resultado[1].listProcessoVinculadoDTO\">{{processoVinculado.dsProcessoVinculadoDTO}}</div></td><td md-cell><div ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\" ng-repeat=\"processoVinculado in resultado[2].descritoesInformacionaisIconograficoDTO.listProcessoVinculadoDTO\">{{processoVinculado.dsProcessoVinculadoDTO}}</div><div ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\" ng-repeat=\"processoVinculado in resultado[2].listProcessoVinculadoDTO\">{{processoVinculado.dsProcessoVinculadoDTO}}</div></td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Órgão Emissor</td><td md-cell>Órgão Emissor</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoresInformacionaisDTO.dsOrgaoEmissorDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].dsOrgaoEmissorDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoresInformacionaisDTO.dsOrgaoEmissorDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].dsOrgaoEmissorDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Autor do Original</td><td md-cell><div ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\" ng-repeat=\"autorOriginal in resultado[1].descritoesInformacionaisIconograficoDTO.listAutorOriginalDTO\">{{autorOriginal.dsAutorOriginalDTO}}</div><div ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\" ng-repeat=\"autorOriginal in resultado[1].listAutorOriginalDTO\">{{autorOriginal.dsAutorOriginalDTO}}</div></td><td md-cell><div ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\" ng-repeat=\"autorOriginal in resultado[2].descritoesInformacionaisIconograficoDTO.listAutorOriginalDTO\">{{autorOriginal.dsAutorOriginalDTO}}</div><div ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\" ng-repeat=\"autorOriginal in resultado[2].listAutorOriginalDTO\">{{autorOriginal.dsAutorOriginalDTO}}</div></td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Data do Original</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.dtOriginalDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].dtOriginalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.dtOriginalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].dtOriginalDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Autor da Cópia</td><td md-cell><div ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\" ng-repeat=\"autorCopia in resultado[1].descritoesInformacionaisIconograficoDTO.listAutorCopiaDTO\">{{autorCopia.dsAutorCopiaDTO}}</div><div ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\" ng-repeat=\"autorCopia in resultado[1].listAutorCopiaDTO\">{{autorCopia.dsAutorCopiaDTO}}</div></td><td md-cell><div ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\" ng-repeat=\"autorCopia in resultado[2].descritoesInformacionaisIconograficoDTO.listAutorCopiaDTO\">{{autorCopia.dsAutorCopiaDTO}}</div><div ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\" ng-repeat=\"autorCopia in resultado[2].listAutorCopiaDTO\">{{autorCopia.dsAutorCopiaDTO}}</div></td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Data da Cópia</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.dtCopiaDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].dtCopiaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.dtCopiaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].dtCopiaDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Escala Cartográfica</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.nuEscalaCartograficaDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].nuEscalaCartograficaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.nuEscalaCartograficaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].nuEscalaCartograficaDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Área Total</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.nuAreaTotalDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].nuAreaTotalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.nuAreaTotalDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].nuAreaTotalDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Unidade de Medida</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.unidadeMedidaDTO.dsUnidadeMedidaDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].unidadeMedidaDTO.dsUnidadeMedidaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.unidadeMedidaDTO.dsUnidadeMedidaDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].unidadeMedidaDTO.dsUnidadeMedidaDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Área Equivalente (m²)</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.nuAreaEquivalenteDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].nuAreaEquivalenteDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.nuAreaEquivalenteDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].nuAreaEquivalenteDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Agrupamento Temático</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.agrupamentoTematicoDTO.dsAgrupamentoTematicoDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].agrupamentoTematicoDTO.dsAgrupamentoTematicoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.agrupamentoTematicoDTO.dsAgrupamentoTematicoDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].agrupamentoTematicoDTO.dsAgrupamentoTematicoDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Informações Complementares</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\">{{resultado[1].descritoesInformacionaisIconograficoDTO.dsInformacoesComplementaresDTO}}</td><td md-cell ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\">{{resultado[1].dsInformacoesComplementaresDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\">{{resultado[2].descritoesInformacionaisIconograficoDTO.dsInformacoesComplementaresDTO}}</td><td md-cell ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\">{{resultado[2].dsInformacoesComplementaresDTO}}</td></tr><tr md-row ng-show=\"(resultado[1].categoriaDTO.idCategoriaDTO == 2 || resultado[1].idDescritoesInformacionaisIconograficoDTO != null)\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 8\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 11\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 12)\"><td md-cell>Palavras-Chave</td><td md-cell><div ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO != null\" ng-repeat=\"palavra in resultado[1].descritoesInformacionaisIconograficoDTO.listPalavraChaveDTO\">{{palavra.dsPalavraChaveDTO}}</div><div ng-show=\"resultado[1].descritoesInformacionaisIconograficoDTO == null\" ng-repeat=\"palavra in resultado[1].listPalavraChaveDTO\">{{palavra.dsPalavraChaveDTO}}</div></td><td md-cell><div ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO != null\" ng-repeat=\"palavra in resultado[2].descritoesInformacionaisIconograficoDTO.listPalavraChaveDTO\">{{palavra.dsPalavraChaveDTO}}</div><div ng-show=\"resultado[2].descritoesInformacionaisIconograficoDTO == null\" ng-repeat=\"palavra in resultado[2].listPalavraChaveDTO\">{{palavra.dsPalavraChaveDTO}}</div></td></tr><!-- Descritores catalográficos - Campos Textual --><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Estante</td><td md-cell>{{resultado[1].dsEstanteDTO}}</td><td md-cell>{{resultado[2].dsEstanteDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Prateleira</td><td md-cell>{{resultado[1].dsPrateleiraDTO}}</td><td md-cell>{{resultado[2].dsPrateleiraDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Caixa</td><td md-cell>{{resultado[1].dsCaixaDTO}}</td><td md-cell>{{resultado[2].dsCaixaDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Notação Kardex</td><td md-cell>{{resultado[1].icNotacaoKardexDTO | simNao}}</td><td md-cell>{{resultado[2].icNotacaoKardexDTO | simNao}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Kardex</td><td md-cell><div ng-repeat=\"kardex in resultado[1].listKardexDTO\">Armário: {{kardex.dsArmarioDTO}} - Gaveta: {{kardex.dsGavetaDTO}} - Maço: {{kardex.dsMacoDTO}}</div></td><td md-cell><div ng-repeat=\"kardex in resultado[2].listKardexDTO\">Armário: {{kardex.dsArmarioDTO}} - Gaveta: {{kardex.dsGavetaDTO}} - Maço: {{kardex.dsMacoDTO}}</div></td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Dimensão do Suporte</td><td md-cell>{{resultado[1].dimensaoSuporteDTO.dsDimensaoSuporteDTO}}</td><td md-cell>{{resultado[2].dimensaoSuporteDTO.dsDimensaoSuporteDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Dimensão do Suporte</td><td md-cell>{{resultado[1].condicaoOriginalDTO.dsCondicaoOriginalDTO}}</td><td md-cell>{{resultado[2].condicaoOriginalDTO.dsCondicaoOriginalDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Iconografia</td><td md-cell>{{resultado[1].icIconografiaDTO}}</td><td md-cell>{{resultado[2].icIconografiaDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Espécie</td><td md-cell>{{resultado[1].icEspecieCartografiaDTO ? \"Cartografia \" : \"\" }} {{resultado[1].icEspecieFotografiaDTO ? \" Fotografia\" : \"\" }}</td><td md-cell>{{resultado[2].icEspecieCartografiaDTO ? \"Cartografia \" : \"\" }} {{resultado[2].icEspecieFotografiaDTO ? \" Fotografia\" : \"\" }}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Informações Complementares</td><td md-cell>{{resultado[1].txInformacoesComplementaresDTO}}</td><td md-cell>{{resultado[2].txInformacoesComplementaresDTO}}</td></tr><!-- Descritores técnicos --><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Possui Matriz .TIFF?</td><td md-cell>{{resultado[1].icTiffDTO | simNao}}</td><td md-cell>{{resultado[2].icTiffDTO | simNao}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Data de Produção da Matriz do Representante Digital</td><td md-cell>{{resultado[1].infoTiffDTO.dtProdMatrizDTO}}</td><td md-cell>{{resultado[2].infoTiffDTO.dtProdMatrizDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Tipo de Equipamento Utilizado</td><td md-cell>{{resultado[1].infoTiffDTO.equipamentoUtilizadoDTO.dsEquipamentoUtilizado}}</td><td md-cell>{{resultado[2].infoTiffDTO.equipamentoUtilizadoDTO.dsEquipamentoUtilizado}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Dimensão da Imagem em Relação ao Original</td><td md-cell>{{resultado[1].infoTiffDTO.dimensaoImgRelacaoDTO.dsDimensaoImgRelacao}}</td><td md-cell>{{resultado[2].infoTiffDTO.dimensaoImgRelacaoDTO.dsDimensaoImgRelacao}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Modo Cores</td><td md-cell>{{resultado[1].infoTiffDTO.modoCoresDTO.dsModoCoresDTO}}</td><td md-cell>{{resultado[2].infoTiffDTO.modoCoresDTO.dsModoCoresDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Resolução Linear Adotada</td><td md-cell>{{resultado[1].infoTiffDTO.resolucaoLinearDTO.dsResolucaoLinearDTO}}</td><td md-cell>{{resultado[2].infoTiffDTO.resolucaoLinearDTO.dsResolucaoLinearDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Resolução Tonal</td><td md-cell>{{resultado[1].infoTiffDTO.resolucaoTonalDTO.dsResolucaoTonalDTO}}</td><td md-cell>{{resultado[2].infoTiffDTO.resolucaoTonalDTO.dsResolucaoTonalDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Houve Processo de Interpolação</td><td md-cell>{{resultado[1].infoTiffDTO.icProcessoInterpolacaoDTO | simNao}}</td><td md-cell>{{resultado[2].infoTiffDTO.icProcessoInterpolacaoDTO | simNao}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Software de Captura</td><td md-cell>{{resultado[1].infoTiffDTO.dsSoftwareCapturaDTO}}</td><td md-cell>{{resultado[2].infoTiffDTO.dsSoftwareCapturaDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Software de Processamento de Imagem</td><td md-cell>{{resultado[1].infoTiffDTO.dsSoftwareProcImgDTO}}</td><td md-cell>{{resultado[2].infoTiffDTO.dsSoftwareProcImgDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Software Operacional Utilizado</td><td md-cell>{{resultado[1].infoTiffDTO.dsSistemaOpDTO}}</td><td md-cell>{{resultado[2].infoTiffDTO.dsSistemaOpDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Tamanho do Arquivo</td><td md-cell>{{resultado[1].infoTiffDTO.nuTamArquivoDTO}}</td><td md-cell>{{resultado[2].infoTiffDTO.nuTamArquivoDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Matriz - Responsável pela Produção da Matriz do Responsável Digital</td><td md-cell>{{resultado[1].infoTiffDTO.dsRespProdMatrizDTO}}</td><td md-cell>{{resultado[2].infoTiffDTO.dsRespProdMatrizDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Possui Derivada .PDF?</td><td md-cell>{{resultado[1].icPdfDTO | simNao}}</td><td md-cell>{{resultado[2].icPdfDTO | simNao}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Derivada PDF - Data de Produção das Derivadas</td><td md-cell>{{resultado[1].infoPdfDTO.dtProdPdfDTO | date : \"dd/MM/yyyy\"}}</td><td md-cell>{{resultado[2].infoPdfDTO.dtProdPdfDTO | date : \"dd/MM/yyyy\"}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Derivada PDF - Software de Geração das Derivadas</td><td md-cell>{{resultado[1].infoPdfDTO.dsSoftGerPdfDTO}}</td><td md-cell>{{resultado[2].infoPdfDTO.dsSoftGerPdfDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Derivada PDF - Responsável pela Produção das Derivadas</td><td md-cell>{{resultado[1].infoPdfDTO.dsRespProdPdfDTO}}</td><td md-cell>{{resultado[2].infoPdfDTO.dsRespProdPdfDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Possui Derivada .JPEG?</td><td md-cell>{{resultado[1].icJpegDTO | simNao}}</td><td md-cell>{{resultado[2].icJpegDTO | simNao}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Derivada JPEG - Data de Produção das Derivadas</td><td md-cell>{{resultado[1].infoJpegDTO.dtProdJpegDTO | date : \"dd/MM/yyyy\"}}</td><td md-cell>{{resultado[2].infoJpegDTO.dtProdJpegDTO | date : \"dd/MM/yyyy\"}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Derivada JPEG - Software de Geração das Derivas</td><td md-cell>{{resultado[1].infoJpegDTO.dsSoftGerJpegDTO}}</td><td md-cell>{{resultado[2].infoJpegDTO.dsSoftGerJpegDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Derivada JPEG - Responsável pela Produção das Derivadas</td><td md-cell>{{resultado[1].infoJpegDTO.dsRespProdJpegDTO}}</td><td md-cell>{{resultado[2].infoJpegDTO.dsRespProdJpegDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Data de Cadastramento</td><td md-cell>{{resultado[1].dtCadastramentoDTO | date : \"dd/MM/yyyy\"}}</td><td md-cell>{{resultado[2].dtCadastramentoDTO | date : \"dd/MM/yyyy\"}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Responsável pelo Cadastramento</td><td md-cell>{{resultado[1].dsRespCadastramentoDTO}}</td><td md-cell>{{resultado[2].dsRespCadastramentoDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorTecnicoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 13\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 14)\"><td md-cell>Informações Complementares</td><td md-cell>{{resultado[1].dsInfoComplDTO}}</td><td md-cell>{{resultado[2].dsInfoComplDTO}}</td></tr><!-- Descritores catalográficos - Campos iconográficos --><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Item</td><td md-cell>{{resultado[1].dsItemDTO}}</td><td md-cell>{{resultado[2].dsItemDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Mapoteca</td><td md-cell>{{resultado[1].dsMapotecaDTO}}</td><td md-cell>{{resultado[2].dsMapotecaDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Gaveta</td><td md-cell>{{resultado[1].dsMapotecaDTO}}</td><td md-cell>{{resultado[2].dsMapotecaDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Prancha</td><td md-cell>{{resultado[1].dsPranchaDTO}}</td><td md-cell>{{resultado[2].dsPranchaDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Notação Antiga - Planta</td><td md-cell>{{resultado[1].dsPlantaDTO}}</td><td md-cell>{{resultado[2].dsPlantaDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Notação Antiga - Armário</td><td md-cell>{{resultado[1].dsArmarioDTO}}</td><td md-cell>{{resultado[2].dsArmarioDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Notação Antiga - Tela</td><td md-cell>{{resultado[1].dsTelaDTO}}</td><td md-cell>{{resultado[2].dsTelaDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Notação Antiga - Tubo</td><td md-cell>{{resultado[1].dsTuboDTO}}</td><td md-cell>{{resultado[2].dsTuboDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Documento</td><td md-cell>{{resultado[1].documentoDTO.dsDocumentoDTO}}</td><td md-cell>{{resultado[2].documentoDTO.dsDocumentoDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Órgão de Origem</td><td md-cell>{{resultado[1].dsOrgaoOrigemDTO}}</td><td md-cell>{{resultado[2].dsOrgaoOrigemDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Órgão de Tutela</td><td md-cell>{{resultado[1].dsOrgaoTutelaDTO}}</td><td md-cell>{{resultado[2].dsOrgaoTutelaDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Material do Suporte</td><td md-cell>{{resultado[1].materialSuporteDTO.dsMaterialSuporteDTO}}</td><td md-cell>{{resultado[2].materialSuporteDTO.dsMaterialSuporteDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Dimensão do Suporte</td><td md-cell>{{resultado[1].dimensaoSuporteDTO.dsDimensaoSuporteDTO}}</td><td md-cell>{{resultado[2].dimensaoSuporteDTO.dsDimensaoSuporteDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Condição do Original</td><td md-cell>{{resultado[1].condicaoOriginalDTO.dsCondicaoOriginalDTO}}</td><td md-cell>{{resultado[2].condicaoOriginalDTO.dsCondicaoOriginalDTO}}</td></tr><tr md-row ng-show=\"resultado[1].idDescritorCatalograficoIconograficoDTO != null\n                  && (resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 9\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 10)\"><td md-cell>Informações Complementares</td><td md-cell>{{resultado[1].descritorCatalograficoIconograficoDTO.dsInfoComplementaresDTO}}</td><td md-cell>{{resultado[2].descritorCatalograficoIconograficoDTO.dsInfoComplementaresDTO}}</td></tr><!-- Alteração arquivos --><tr md-row ng-show=\"resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 18\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 19\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 20\"><td md-cell>Tipo</td><td md-cell>{{resultado[1].dsTipoArquivoDTO}}</td><td md-cell>{{resultado[2].dsTipoArquivoDTO}}</td></tr><tr md-row ng-show=\"resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 18\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 19\n                  || resultado[0].tipoHistoricoDTO.idTipoHistoricoDTO == 20\"><td md-cell>Nome</td><td md-cell>{{resultado[1].dsArquivoAnexoDTO}}</td><td md-cell>{{resultado[2].dsArquivoAnexoDTO}}</td></tr></tbody></table></md-table-container></div></fieldset><div layout=row layout-align=\"center none\" flex=100><md-button class=md-raised ng-click=fechar()><md-icon md-font-set=material-icons>clear</md-icon>Fechar</md-button></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-acervo/app/cadastro-documento/view/modal-novo-arquivo-anexo.tmpl.html","<p md-dialog-title>Teste</p><md-dialog flex=70 aria-label=\"{{\'label-gestor\' | translate}}\" ng-cloak><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>Cadastro do Anexo</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><div style=\"padding: 42px\"><ng-form name=form enctype=multipart/form-data><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=40><label>Tipo</label><input ng-model=arquivoAnexoDTO.dsFormatoDTO required></md-input-container></div><div layout=row layout-align=\"center none\" flex=100><md-input-container flex=40><label>Nome</label><input ng-model=arquivoAnexoDTO.dsArquivoAnexoDTO required></md-input-container></div><md-table-container ng-show=isAlteracaoArquivo()><table md-table><thead md-head><tr md-row><th md-column class=column-width-min>Descrição</th><th md-column class=column-width-min>Ação</th></tr></thead><tbody md-body><tr md-row><td md-cell>{{arquivoAnexoDTO.dsArquivoGravadoDTO}}</td><td md-cell><ng-md-icon icon=mode_edit size=19 alt=EDITAR class=space-icon ng-click=alterarAnexo()></ng-md-icon></td></tr></tbody></table></md-table-container><div layout=row layout-align=\"center none\" flex=100 ng-show=!isAlteracaoArquivo()><md-container flex=40><input type=file ngf-select ng-model=arquivoSelecionado name=file class=button-upload accept=/* required ngf-model-invalid=errorFile></md-container></div><br><br><div layout=row layout-align=\"center none\" flex=100><md-button class=md-raised ng-click=fechar()><md-icon md-font-set=material-icons>clear</md-icon>Cancelar</md-button><md-button class=md-raised ng-click=anexar(form)><md-icon md-font-set=material-icons>save</md-icon>Confirmar</md-button></div></ng-form></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-acervo/app/consultar/view/consultar.tmpl.html","<div class=acervo-consultar><fieldset><legend id=tituloPainelPesquisa>Filtros de Pesquisa</legend><div layout=row layout-align=\"start center\"><md-input-container flex=10><label>Categoria</label><md-select ng-model=arquivoAcertoRestricoes.categoria ng-change=onChangeCategoria()><md-option ng-repeat=\"categoria in categorias\" ng-value=categoria.idCategoriaDTO>{{categoria.dsCategoriaDTO}}</md-option></md-select></md-input-container><div flex=5></div><md-input-container flex=15><label>Espécie Documental</label><md-select ng-model=arquivoAcertoRestricoes.especieDocumental ng-change=onChangeEspecieDocumental() ng-disabled=\"arquivoAcertoRestricoes.categoria != 1\"><md-option ng-repeat=\"especieDocumental in especieDocumentais\" ng-value=especieDocumental.idEspecieDocumentalDTO>{{especieDocumental.dsEspecieDocumentalDTO}}</md-option></md-select></md-input-container><div flex=5></div><md-input-container flex=20><label>Descritores Informacionais</label><md-select ng-model=arquivoAcertoRestricoes.descritorInformacional ng-disabled=\"arquivoAcertoRestricoes.descritorCatalografico != null || arquivoAcertoRestricoes.categoria == null\"><md-option ng-repeat=\"descritorInformacional in descritoresInformacionais\" ng-value=descritorInformacional>{{descritorInformacional.descricao}}</md-option></md-select></md-input-container><div flex=5></div><md-input-container flex=20><label>Descritores Catalográficos</label><md-select ng-model=arquivoAcertoRestricoes.descritorCatalografico ng-disabled=\"arquivoAcertoRestricoes.descritorInformacional != null\"><md-option ng-repeat=\"descritorCatalografico in descritoresCatalograficos\" ng-value=descritorCatalografico>{{descritorCatalografico.descricao}}</md-option></md-select></md-input-container><div flex=5></div><md-button flex=15 id=botaoAddFiltro class=\"md-raised md-primary\" ng-click=addFiltro()><md-icon md-font-set=material-icons>add</md-icon>Adicionar Filtro</md-button></div><div layout=row layout-align=\"start center\"><md-input-container flex=40><label>Buscar, ex: protocolo acervo, palavras-chave, NUP, RIP</label><input ng-model=arquivoAcertoRestricoes.texto ng-disabled=\"(arquivoAcertoRestricoes.descritorInformacional && !arquivoAcertoRestricoes.descritorInformacional.texto)\n                         || (arquivoAcertoRestricoes.descritorCatalografico && !arquivoAcertoRestricoes.descritorCatalografico.texto)\"></md-input-container><div flex=5></div><md-input-container flex=25><md-datepicker ng-model=arquivoAcertoRestricoes.minimo md-placeholder=DE md-open-on-focus ng-if=true ng-disabled=\"(arquivoAcertoRestricoes.descritorInformacional && arquivoAcertoRestricoes.descritorInformacional.texto)\n                        || (arquivoAcertoRestricoes.descritorCatalografico && arquivoAcertoRestricoes.descritorCatalografico.texto)\"></md-datepicker></md-input-container><div flex=5></div><md-input-container flex=25><md-datepicker ng-model=arquivoAcertoRestricoes.maximo md-placeholder=ATÉ md-open-on-focus ng-if=true ng-disabled=\"(arquivoAcertoRestricoes.descritorInformacional && arquivoAcertoRestricoes.descritorInformacional.texto)\n                        || (arquivoAcertoRestricoes.descritorCatalografico && arquivoAcertoRestricoes.descritorCatalografico.texto)\"></md-datepicker></md-input-container></div><div layout=row layout-align=\"start center\"><md-input-container flex=30><label>Protocolo Acervo</label><input type=text ng-model=protocoloAcertoRestricoes.texto mask=9999.9.9.99999-99 clean=true></md-input-container></div><div layout=row><b>Filtrando por:</b></div><div layout=row><md-button class=\"md-raised md-primary\" ng-click=removerFiltro(filtro.id) ng-repeat=\"filtro in filterMapping\">{{filtro.descricao}}<md-icon md-font-set=material-icons>clear</md-icon></md-button></div><div layout=row layout-align=\"end center\"><md-button class=md-raised ng-click=limparPesquisa()>Limpar</md-button><md-button id=botaoPesquisar class=md-raised ng-click=pesquisar()><md-icon md-font-set=material-icons>search</md-icon>Pesquisar</md-button></div><hr ng-if=\"resultados.length > 0\"><div layout=column flex=100 ng-if=\"resultados.length > 0\"><div layout=row layout-align=\"space-between center\"><div><b ng-show=\"resultados[0].totalPaginasConsulta > 0\">{{resultados[0].totalElementosConsulta}} item(ns) encontrado(s). Mostrando página</b><md-input-container ng-show=\"resultados[0].totalPaginasConsulta > 0\"><label>{{resultados[0].paginaAtualConsulta}}</label><md-select ng-model=resultados[0].paginaAtualConsulta ng-change=pesquisarPaginaCombo()><md-option ng-repeat=\"pg in resultados[0].listaPaginas\" ng-value={{pg}}>{{pg}}</md-option></md-select></md-input-container><b ng-show=\"resultados[0].totalPaginasConsulta > 0\">de {{resultados[0].totalPaginasConsulta}}</b></div><b ng-show=\"resultados[0].totalPaginasConsulta <= 0 \">{{resultados.length}} item(ns) encontrado(s).</b></div><div layout=row layout-align=\"space-between center\"><md-button class=md-raised ng-show=\"resultados[0].paginaAtualConsulta > 0\" ng-click=paginaAnterior()>Página Anterior</md-button><md-button class=md-raised ng-show=\"resultados[0].paginaAtualConsulta + 1 <= resultados[0].totalPaginasConsulta\" ng-click=proximaPagina()>Proxima Página</md-button></div><div layout=row><md-content><md-table-container><table md-table id=idtabelaPesquisa><thead md-head><tr md-row><th md-column class=column-width-max>Protocolo Acervo</th><th md-column class=column-width-max>Resultados Obtidos</th><th md-column class=column-width-max>Ação</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"resultado in resultados\"><td md-cell>{{resultado.coProtocoloAcervoDTO | numeroProtocoloAcervo}}</td><td md-cell><!-- Processos --><div ng-if=\"resultado.categoriaDTO.idCategoriaDTO == 1 && resultado.descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 1\"><a ng-click=goVisualizarDocumento(resultado.idArquivoAcervoDTO) class=link-resultados-obtidos>Processo, <span ng-show=resultado.descritoresInformacionaisDTO.dsProcessoAdmnistrativoDTO>{{resultado.descritoresInformacionaisDTO.dsProcessoAdmnistrativoDTO}}, </span><span ng-show=resultado.descritoresInformacionaisDTO.dsTituloFormalDTO>{{resultado.descritoresInformacionaisDTO.dsTituloFormalDTO}}, </span><span ng-show=resultado.descritoresInformacionaisDTO.assuntoDTO.dsAssuntoDTO>{{resultado.descritoresInformacionaisDTO.assuntoDTO.dsAssuntoDTO}}, </span><span ng-show=resultado.descritoresInformacionaisDTO.dtAtuacaoProcessoDTO>{{resultado.descritoresInformacionaisDTO.dtAtuacaoProcessoDTO}}</span></a><div><span ng-repeat=\"interessado in resultado.descritoresInformacionaisDTO.listInteressadosDTO\">{{interessado.dsInteressadosDTO}}, </span><span ng-show=resultado.descritoresInformacionaisDTO.unidadeFederativaDTO.dsUnidadeFederativaDTO>{{resultado.descritoresInformacionaisDTO.unidadeFederativaDTO.dsUnidadeFederativaDTO}}</span></div><div class=palavras-chave-resultados-obtidos>Palavras-chave: {{resultado.descritoresInformacionaisDTO.palavraChavesDTO | cincoPalavrasChave}}</div></div><!-- Fichas Financeiras --><div ng-if=\"resultado.categoriaDTO.idCategoriaDTO == 1 && resultado.descritoresInformacionaisDTO.especieDocumentalDTO.idEspecieDocumentalDTO == 2\"><a ng-click=goVisualizarDocumento(resultado.idArquivoAcervoDTO) class=link-resultados-obtidos>Ficha Financeira</a><div><span ng-show=resultado.descritoresInformacionaisDTO.nuRipSpiuDTO>{{resultado.descritoresInformacionaisDTO.nuRipSpiuDTO}}, </span><span ng-show=resultado.descritoresInformacionaisDTO.nuRipSiapaDTO>{{resultado.descritoresInformacionaisDTO.nuRipSiapaDTO}}, </span><span ng-show=resultado.descritoresInformacionaisDTO.dsLogradouroOriginalDTO>{{resultado.descritoresInformacionaisDTO.dsLogradouroOriginalDTO}}</span></div><div>Informações Complementares: {{resultado.descritoresInformacionaisDTO.dsInfoComplementaresDTO | limitTo:200}}</div></div><!-- Iconográfico --><div ng-if=\"resultado.categoriaDTO.idCategoriaDTO == 2\"><a ng-click=goVisualizarDocumento(resultado.idArquivoAcervoDTO) class=link-resultados-obtidos><span ng-show=resultado.descritoesInformacionaisIconograficoDTO.dsTipoDocumentoDTO>{{resultado.descritoesInformacionaisIconograficoDTO.dsTipoDocumentoDTO}}, </span><span ng-show=resultado.descritoesInformacionaisIconograficoDTO.tipoDocumentalDTO.dsTipoDocumentalDTO>{{resultado.descritoesInformacionaisIconograficoDTO.tipoDocumentalDTO.dsTipoDocumentalDTO}}, </span><span ng-show=resultado.descritoesInformacionaisIconograficoDTO.unidadeFederativaDTO.dsUnidadeFederativaDTO>{{resultado.descritoesInformacionaisIconograficoDTO.unidadeFederativaDTO.dsUnidadeFederativaDTO}}</span></a><div><span ng-show=resultado.descritoesInformacionaisIconograficoDTO.dsOrgaoEmissorDTO>{{resultado.descritoesInformacionaisIconograficoDTO.dsOrgaoEmissorDTO}}, </span><span ng-show=resultado.descritoesInformacionaisIconograficoDTO.dtOriginalDTO>{{resultado.descritoesInformacionaisIconograficoDTO.dtOriginalDTO}}, </span><span ng-show=resultado.descritoesInformacionaisIconograficoDTO.nuEscalaCartograficaDTO>{{resultado.descritoesInformacionaisIconograficoDTO.nuEscalaCartograficaDTO}}, </span><span ng-show=resultado.descritoesInformacionaisIconograficoDTO.nuAreaTotalDTO>{{resultado.descritoesInformacionaisIconograficoDTO.nuAreaTotalDTO}}, </span><span ng-show=resultado.descritorCatalograficoIconograficoDTO.dsOrgaoTutelaDTO>Tutela, {{resultado.descritorCatalograficoIconograficoDTO.dsOrgaoTutelaDTO}}</span></div><div class=palavras-chave-resultados-obtidos>Palavras-chave: {{resultado.descritoesInformacionaisIconograficoDTO.listPalavraChaveDTO | cincoPalavrasChave}}</div></div></td><td md-cell><ng-md-icon icon=mode_edit size=19 alt=EDITAR class=space-icon ng-click=goAlterarDocumento(resultado.idArquivoAcervoDTO) ng-show=\"resultado.situacaoDTO.idSituacaoDTO == 1\"></ng-md-icon><ng-md-icon icon=settings size=19 alt=GERENCIAL class=space-icon ng-click=goEditarDocumento(resultado.idArquivoAcervoDTO) ng-show=\"resultado.situacaoDTO.idSituacaoDTO == 1\"></ng-md-icon></td></tr></tbody></table></md-table-container></md-content></div></div></fieldset><div layout=row layout-align=\"end center\"><md-button class=md-raised ng-click=goCadastrarDocumento()><md-icon md-font-set=material-icons>add</md-icon>Novo</md-button></div></div>");
$templateCache.put("scripts-acervo/app/custom-components/botoes-cadastro/botoes-cadastro.tmpl.html","<div layout=row layout-sm=column layout-align=\"end center\" layout-wrap><md-button id=botaoVoltar class=md-raised ng-click=previous() ng-if=showPrevious()><md-icon md-font-set=material-icons>arrow_back</md-icon>Voltar</md-button><md-button id=botaoCancelar class=md-raised ng-click=cancelar()><md-icon md-font-set=material-icons>clear</md-icon>Cancelar</md-button><md-button id=botaoSalvar class=md-raised ng-click=save() ng-if=showSave()><md-icon md-font-set=material-icons>save</md-icon>Salvar</md-button><md-button id=botaoProximo class=md-raised ng-click=next() ng-if=showNext()><md-icon md-font-set=material-icons>arrow_forward</md-icon>Próximo</md-button></div>");
$templateCache.put("scripts-acervo/app/custom-components/multi-composite-field/multi-composite-field.tmpl.html","<div layout=row flex=100 layout-align=\"center none\" style=\"min-height: {{70 * model.length + \'px\'}}\"><div class=multi-composite-field layout=row flex={{flex}}><div layout=column layout-align=\"none none\" flex=85><div layout=row ng-repeat=\"value in model track by $index\" layout-align=\"center top\" style=\"min-height: 70px\"><md-input-container flex=90><label>{{label}} nº.: {{$index + 1}}</label><input id=\"{{name + $index}}\" ng-model=value[name] ng-disabled=disabled ng-required=obrigatorio><div ng-messages=value[name].$error><div ng-message=required class=my-message>Campo obrigatório!</div></div></md-input-container><div flex=10><md-button ng-if=\"$index !== 0\" class=\"md-fab md-mini multi-composite-field-remove\" ng-click=remove($index) ng-disabled=disabled><md-icon>remove</md-icon></md-button></div></div></div><div layout=column layout-align=\"center center\" flex=15><md-button id=\"botaoAdd{{name + $index}}\" class=\"md-fab md-mini multi-composite-field-add\" ng-click=add() ng-disabled=disabled><md-icon>add</md-icon></md-button></div></div></div>");
$templateCache.put("scripts-acervo/app/custom-components/partial-date-field/partial-date-field.tmpl.html","<div layout=row layout-align=\"center none\" class=gray-text-color><div layout=column flex={{flex}}><span style=\"margin-left: 3px\">{{label}}<span ng-if=obrigatorio> *</span></span><div flex=100 layout-align=\"space-between start\"><md-input-container flex=25><label>Dia</label><input id=dia{{id}} ng-model=dia ng-change=onChange() ng-disabled=desabilitado numbers-only maxlength=2></md-input-container><span>/</span><md-input-container flex=25><label>Mês</label><input id=mes{{id}} ng-model=mes ng-change=onChange() ng-disabled=desabilitado numbers-only maxlength=2></md-input-container><span>/</span><md-input-container flex=40 ng-show=!isAceitaNumero()><label>Ano</label><input id=ano{{id}} ng-model=ano ng-change=onChange() ng-disabled=desabilitado maxlength=15></md-input-container><md-input-container flex=40 ng-show=isAceitaNumero()><label>Ano</label><input id=anonum{{id}} ng-model=ano ng-change=onChange() ng-disabled=desabilitado numbers-only maxlength=4></md-input-container></div></div></div>");
$templateCache.put("scripts-acervo/app/editar-documento/view/editar-documento.tmpl.html","<div><fieldset><legend>Editar Informações</legend><h2>Documentos Cadastrados</h2><div layout=row layout-align=\"center none\"><div flex=100><md-table-container><table md-table><thead md-head><tr md-row><th md-column>Protocolo Acervo</th><th md-column>Tipo</th><th md-column>Nome</th><th md-column>Formato</th><th md-column>Ações</th><th md-column>Documento Principal</th><th md-column>Publicação na Internet</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"arquivoAcervo in listaArquivosAcervosMaisFilhos | filter: {situacaoDTO : {idSituacaoDTO: 1}}\"><td md-cell>{{arquivoAcervo.coProtocoloAcervoDTOFormatada}} <a ng-if=\"arquivoAcervo.exibirPrincipalDTO && isPossuiAcervosAssociados(arquivoAcervo)\" ng-click=goVisualizarRelacionados(arquivoAcervo) class=link-visualizar-relacionados><span>Visualizar relacionados</span></a></td><td md-cell>{{mostrarInformacoesArquivoAnexo(arquivoAcervo).tipo}}</td><td md-cell>{{mostrarInformacoesArquivoAnexo(arquivoAcervo).nome}}</td><td md-cell>{{mostrarInformacoesArquivoAnexo(arquivoAcervo).formato}} <a ng-if=isPossuiMaisAnexos(arquivoAcervo) ng-click=goVisualizarAnexos(arquivoAcervo) class=link-visualizar-relacionados><span>Visualizar outros arquivos</span></a></td><td md-cell layout-align=\"center center\" style=\"width: 20%\"><md-icon id=iconeEditarAcervo ng-click=editar(arquivoAcervo.idArquivoAcervoDTO) class=space-icon>mode_edit</md-icon><md-icon ng-click=visualizar(arquivoAcervo.idArquivoAcervoDTO) class=space-icon>search</md-icon><md-icon ng-click=deletarArquivoAcervo(arquivoAcervo.idArquivoAcervoDTO) class=space-icon>delete</md-icon><md-icon ng-click=gerarEtiqueta(arquivoAcervo.idArquivoAcervoDTO) class=space-icon>print</md-icon></td><td md-cell><md-checkbox id=checkBoxPrincipal{{$index}} ng-model=arquivoAcervo.exibirPrincipalDTO class=md-primary ng-change=onChangePrincipal(arquivoAcervo.idArquivoAcervoDTO) ng-disabled=arquivoAcervo.icPrincipalDTO></td><td md-cell><md-radio-group ng-model=arquivoAcervo.icPublicadoInternetDTO layout=row ng-change=alterarPublicacaoInternet(arquivoAcervo)><md-radio-button ng-value=true class=md-primary>Ativo</md-radio-button><md-radio-button ng-value=false>Inativo</md-radio-button></md-radio-group></td></tr></tbody></table></md-table-container><div style=\"text-align: right\"><md-button id=botaoNovoAcervo class=md-raised ng-click=novo()><md-tooltip md-direction=bottom>Relacionado Série Documental e/ou Assunto Principal</md-tooltip><md-icon md-font-set=material-icons>add</md-icon>Inserir Novo Documento</md-button></div></div></div><h2>Envio para SEI</h2><div layout=row layout-align=\"center none\"><div flex=100><md-table-container><table md-table><thead md-head><tr md-row><th md-column>SEI</th><th md-column>Protocolo Acervo</th><th md-column>Tipo</th><th md-column>Nome</th><th md-column>Formato</th><th md-column>Número do Processo SEI</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"arquivoAnexoLista in listaAnexosAcervosMaisFilhos\"><td md-cell><md-checkbox ng-model=arquivoAnexoLista.icEnviarSEI class=md-primary ng-if=isArquivoPodeIntegrarSei(arquivoAnexoLista)></td><td md-cell>{{arquivoAnexoLista.protocoloAcervoFormatado}}</td><td md-cell>{{arquivoAnexoLista.dsArquivoAnexoDTO}}</td><td md-cell>{{arquivoAnexoLista.dsFormatoDTO}}</td><td md-cell>{{arquivoAnexoLista.dsTipoArquivoDTO}}</td><td md-cell>{{arquivoAnexoLista.protocoloIntegracaoSei}}</td></tr></tbody></table></md-table-container><br clear=all><br clear=all><div layout=row flex=100><md-radio-group ng-model=tipoEnvioSEI layout=column ng-change=alterarTipoEnvioSEI()><md-radio-button ng-value=1>Autuar Processo no SEI</md-radio-button><md-radio-button ng-value=3>Autuar Processo no SEI com Número de Processo informado</md-radio-button><md-radio-button ng-value=2>Anexar a um Processo Existente</md-radio-button></md-radio-group></div><div layout=row flex=100 ng-show=\"tipoEnvioSEI == 2\"><md-input-container flex=90><input ng-model=numeroProcessoExistenteSei ui-mask=99999.999999/9999-99 ui-mask-placeholder ui-mask-placeholder-char=space></md-input-container></div><div layout=row flex=100 ng-show=\"tipoEnvioSEI == 3\"><md-input-container flex=90><input ng-model=numeroProcessoAutuarSei ui-mask=99999.999999/9999-99 ui-mask-placeholder ui-mask-placeholder-char=space></md-input-container></div><br clear=all><div layout=row><md-button class=md-raised ng-click=cancelarEnvioSEI()><md-icon md-font-set=material-icons>clear</md-icon>Cancelar</md-button><md-button class=md-raised ng-click=enviarParaSEI()>Enviar</md-button></div></div></div></fieldset></div>");
$templateCache.put("scripts-acervo/app/editar-documento/view/modal-visualizar-acervos-relacionados.tmpl.html","<p md-dialog-title>Teste</p><md-dialog flex=30 aria-label=\"{{\'label-gestor\' | translate}}\" ng-cloak><md-toolbar class=md-toolbar><div class=md-toolbar-tools><h2>Visualizar Relacionados</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><i class=\"fa fa-times icon-fechar\" aria-hidden=true></i></md-button></div></md-toolbar><md-dialog-content><div style=\"padding: 42px\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column>Protocolo Acervo</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"arquivoAcervo in listaAcervosAssociados | filter: {situacaoDTO : {idSituacaoDTO: 1}}\"><td md-cell><a ng-click=goVisualizarDocumento(arquivoAcervo.idArquivoAcervoDTO) class=link-resultados-obtidos><span>{{arquivoAcervo.coProtocoloAcervoDTOFormatada}}</span></a></td></tr></tbody></table></md-table-container></div></md-dialog-content></md-dialog>");}]);
(function (global) {
    'use strict';

    var util = newUtil();
    var inliner = newInliner();
    var fontFaces = newFontFaces();
    var images = newImages();

    // Default impl options
    var defaultOptions = {
        // Default is to fail on error, no placeholder
        imagePlaceholder: undefined,
        // Default cache bust is false, it will use the cache
        cacheBust: false
    };

    var domtoimage = {
        toSvg: toSvg,
        toPng: toPng,
        toJpeg: toJpeg,
        toBlob: toBlob,
        toPixelData: toPixelData,
        impl: {
            fontFaces: fontFaces,
            images: images,
            util: util,
            inliner: inliner,
            options: {}
        }
    };

    if (typeof module !== 'undefined')
        module.exports = domtoimage;
    else
        global.domtoimage = domtoimage;


    /**
     * @param {Node} node - The DOM Node object to render
     * @param {Object} options - Rendering options
     * @param {Function} options.filter - Should return true if passed node should be included in the output
     *          (excluding node means excluding it's children as well). Not called on the root node.
     * @param {String} options.bgcolor - color for the background, any valid CSS color value.
     * @param {Number} options.width - width to be applied to node before rendering.
     * @param {Number} options.height - height to be applied to node before rendering.
     * @param {Object} options.style - an object whose properties to be copied to node's style before rendering.
     * @param {Number} options.quality - a Number between 0 and 1 indicating image quality (applicable to JPEG only),
                defaults to 1.0.
     * @param {String} options.imagePlaceholder - dataURL to use as a placeholder for failed images, default behaviour is to fail fast on images we can't fetch
     * @param {Boolean} options.cacheBust - set to true to cache bust by appending the time to the request url
     * @return {Promise} - A promise that is fulfilled with a SVG image data URL
     * */
    function toSvg(node, options) {
        options = options || {};
        copyOptions(options);
        return Promise.resolve(node)
            .then(function (node) {
                return cloneNode(node, options.filter, true);
            })
            .then(embedFonts)
            .then(inlineImages)
            .then(applyOptions)
            .then(function (clone) {
                return makeSvgDataUri(clone,
                    options.width || util.width(node),
                    options.height || util.height(node)
                );
            });

        function applyOptions(clone) {
            if (options.bgcolor) clone.style.backgroundColor = options.bgcolor;

            if (options.width) clone.style.width = options.width + 'px';
            if (options.height) clone.style.height = options.height + 'px';

            if (options.style)
                Object.keys(options.style).forEach(function (property) {
                    clone.style[property] = options.style[property];
                });

            return clone;
        }
    }

    /**
     * @param {Node} node - The DOM Node object to render
     * @param {Object} options - Rendering options, @see {@link toSvg}
     * @return {Promise} - A promise that is fulfilled with a Uint8Array containing RGBA pixel data.
     * */
    function toPixelData(node, options) {
        return draw(node, options || {})
            .then(function (canvas) {
                return canvas.getContext('2d').getImageData(
                    0,
                    0,
                    util.width(node),
                    util.height(node)
                ).data;
            });
    }

    /**
     * @param {Node} node - The DOM Node object to render
     * @param {Object} options - Rendering options, @see {@link toSvg}
     * @return {Promise} - A promise that is fulfilled with a PNG image data URL
     * */
    function toPng(node, options) {
        return draw(node, options || {})
            .then(function (canvas) {
                return canvas.toDataURL();
            });
    }

    /**
     * @param {Node} node - The DOM Node object to render
     * @param {Object} options - Rendering options, @see {@link toSvg}
     * @return {Promise} - A promise that is fulfilled with a JPEG image data URL
     * */
    function toJpeg(node, options) {
        options = options || {};
        return draw(node, options)
            .then(function (canvas) {
                return canvas.toDataURL('image/jpeg', options.quality || 1.0);
            });
    }

    /**
     * @param {Node} node - The DOM Node object to render
     * @param {Object} options - Rendering options, @see {@link toSvg}
     * @return {Promise} - A promise that is fulfilled with a PNG image blob
     * */
    function toBlob(node, options) {
        return draw(node, options || {})
            .then(util.canvasToBlob);
    }

    function copyOptions(options) {
        // Copy options to impl options for use in impl
        if(typeof(options.imagePlaceholder) === 'undefined') {
            domtoimage.impl.options.imagePlaceholder = defaultOptions.imagePlaceholder;
        } else {
            domtoimage.impl.options.imagePlaceholder = options.imagePlaceholder;
        }

        if(typeof(options.cacheBust) === 'undefined') {
            domtoimage.impl.options.cacheBust = defaultOptions.cacheBust;
        } else {
            domtoimage.impl.options.cacheBust = options.cacheBust;
        }
    }

    function draw(domNode, options) {
        return toSvg(domNode, options)
            .then(util.makeImage)
            .then(util.delay(100))
            .then(function (image) {
                var canvas = newCanvas(domNode);
                canvas.getContext('2d').drawImage(image, 0, 0);
                return canvas;
            });

        function newCanvas(domNode) {
            var canvas = document.createElement('canvas');
            canvas.width = options.width || util.width(domNode);
            canvas.height = options.height || util.height(domNode);

            if (options.bgcolor) {
                var ctx = canvas.getContext('2d');
                ctx.fillStyle = options.bgcolor;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }

            return canvas;
        }
    }

    function cloneNode(node, filter, root) {
        if (!root && filter && !filter(node)) return Promise.resolve();

        return Promise.resolve(node)
            .then(makeNodeCopy)
            .then(function (clone) {
                return cloneChildren(node, clone, filter);
            })
            .then(function (clone) {
                return processClone(node, clone);
            });

        function makeNodeCopy(node) {
            if (node instanceof HTMLCanvasElement) return util.makeImage(node.toDataURL());
            return node.cloneNode(false);
        }

        function cloneChildren(original, clone, filter) {
            var children = original.childNodes;
            if (children.length === 0) return Promise.resolve(clone);

            return cloneChildrenInOrder(clone, util.asArray(children), filter)
                .then(function () {
                    return clone;
                });

            function cloneChildrenInOrder(parent, children, filter) {
                var done = Promise.resolve();
                children.forEach(function (child) {
                    done = done
                        .then(function () {
                            return cloneNode(child, filter);
                        })
                        .then(function (childClone) {
                            if (childClone) parent.appendChild(childClone);
                        });
                });
                return done;
            }
        }

        function processClone(original, clone) {
            if (!(clone instanceof Element)) return clone;

            return Promise.resolve()
                .then(cloneStyle)
                .then(clonePseudoElements)
                .then(copyUserInput)
                .then(fixSvg)
                .then(function () {
                    return clone;
                });

            function cloneStyle() {
                copyStyle(window.getComputedStyle(original), clone.style);

                function copyStyle(source, target) {
                    if (source.cssText) target.cssText = source.cssText;
                    else copyProperties(source, target);

                    function copyProperties(source, target) {
                        util.asArray(source).forEach(function (name) {
                            target.setProperty(
                                name,
                                source.getPropertyValue(name),
                                source.getPropertyPriority(name)
                            );
                        });
                    }
                }
            }

            function clonePseudoElements() {
                [':before', ':after'].forEach(function (element) {
                    clonePseudoElement(element);
                });

                function clonePseudoElement(element) {
                    var style = window.getComputedStyle(original, element);
                    var content = style.getPropertyValue('content');

                    if (content === '' || content === 'none') return;

                    var className = util.uid();
                    clone.className = clone.className + ' ' + className;
                    var styleElement = document.createElement('style');
                    styleElement.appendChild(formatPseudoElementStyle(className, element, style));
                    clone.appendChild(styleElement);

                    function formatPseudoElementStyle(className, element, style) {
                        var selector = '.' + className + ':' + element;
                        var cssText = style.cssText ? formatCssText(style) : formatCssProperties(style);
                        return document.createTextNode(selector + '{' + cssText + '}');

                        function formatCssText(style) {
                            var content = style.getPropertyValue('content');
                            return style.cssText + ' content: ' + content + ';';
                        }

                        function formatCssProperties(style) {

                            return util.asArray(style)
                                .map(formatProperty)
                                .join('; ') + ';';

                            function formatProperty(name) {
                                return name + ': ' +
                                    style.getPropertyValue(name) +
                                    (style.getPropertyPriority(name) ? ' !important' : '');
                            }
                        }
                    }
                }
            }

            function copyUserInput() {
                if (original instanceof HTMLTextAreaElement) clone.innerHTML = original.value;
                if (original instanceof HTMLInputElement) clone.setAttribute("value", original.value);
            }

            function fixSvg() {
                if (!(clone instanceof SVGElement)) return;
                clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

                if (!(clone instanceof SVGRectElement)) return;
                ['width', 'height'].forEach(function (attribute) {
                    var value = clone.getAttribute(attribute);
                    if (!value) return;

                    clone.style.setProperty(attribute, value);
                });
            }
        }
    }

    function embedFonts(node) {
        return fontFaces.resolveAll()
            .then(function (cssText) {
                var styleNode = document.createElement('style');
                node.appendChild(styleNode);
                styleNode.appendChild(document.createTextNode(cssText));
                return node;
            });
    }

    function inlineImages(node) {
        return images.inlineAll(node)
            .then(function () {
                return node;
            });
    }

    function makeSvgDataUri(node, width, height) {
        return Promise.resolve(node)
            .then(function (node) {
                node.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
                return new XMLSerializer().serializeToString(node);
            })
            .then(util.escapeXhtml)
            .then(function (xhtml) {
                return '<foreignObject x="0" y="0" width="100%" height="100%">' + xhtml + '</foreignObject>';
            })
            .then(function (foreignObject) {
                return '<svg xmlns="http://www.w3.org/2000/svg" width="' + width + '" height="' + height + '">' +
                    foreignObject + '</svg>';
            })
            .then(function (svg) {
                return 'data:image/svg+xml;charset=utf-8,' + svg;
            });
    }

    function newUtil() {
        return {
            escape: escape,
            parseExtension: parseExtension,
            mimeType: mimeType,
            dataAsUrl: dataAsUrl,
            isDataUrl: isDataUrl,
            canvasToBlob: canvasToBlob,
            resolveUrl: resolveUrl,
            getAndEncode: getAndEncode,
            uid: uid(),
            delay: delay,
            asArray: asArray,
            escapeXhtml: escapeXhtml,
            makeImage: makeImage,
            width: width,
            height: height
        };

        function mimes() {
            /*
             * Only WOFF and EOT mime types for fonts are 'real'
             * see http://www.iana.org/assignments/media-types/media-types.xhtml
             */
            var WOFF = 'application/font-woff';
            var JPEG = 'image/jpeg';

            return {
                'woff': WOFF,
                'woff2': WOFF,
                'ttf': 'application/font-truetype',
                'eot': 'application/vnd.ms-fontobject',
                'png': 'image/png',
                'jpg': JPEG,
                'jpeg': JPEG,
                'gif': 'image/gif',
                'tiff': 'image/tiff',
                'svg': 'image/svg+xml'
            };
        }

        function parseExtension(url) {
            var match = /\.([^\.\/]*?)$/g.exec(url);
            if (match) return match[1];
            else return '';
        }

        function mimeType(url) {
            var extension = parseExtension(url).toLowerCase();
            return mimes()[extension] || '';
        }

        function isDataUrl(url) {
            return url.search(/^(data:)/) !== -1;
        }

        function toBlob(canvas) {
            return new Promise(function (resolve) {
                var binaryString = window.atob(canvas.toDataURL().split(',')[1]);
                var length = binaryString.length;
                var binaryArray = new Uint8Array(length);

                for (var i = 0; i < length; i++)
                    binaryArray[i] = binaryString.charCodeAt(i);

                resolve(new Blob([binaryArray], {
                    type: 'image/png'
                }));
            });
        }

        function canvasToBlob(canvas) {
            if (canvas.toBlob)
                return new Promise(function (resolve) {
                    canvas.toBlob(resolve);
                });

            return toBlob(canvas);
        }

        function resolveUrl(url, baseUrl) {
            var doc = document.implementation.createHTMLDocument();
            var base = doc.createElement('base');
            doc.head.appendChild(base);
            var a = doc.createElement('a');
            doc.body.appendChild(a);
            base.href = baseUrl;
            a.href = url;
            return a.href;
        }

        function uid() {
            var index = 0;

            return function () {
                return 'u' + fourRandomChars() + index++;

                function fourRandomChars() {
                    /* see http://stackoverflow.com/a/6248722/2519373 */
                    return ('0000' + (Math.random() * Math.pow(36, 4) << 0).toString(36)).slice(-4);
                }
            };
        }

        function makeImage(uri) {
            return new Promise(function (resolve, reject) {
                var image = new Image();
                image.onload = function () {
                    resolve(image);
                };
                image.onerror = reject;
                image.src = uri;
            });
        }

        function getAndEncode(url) {
            var TIMEOUT = 30000;
            if(domtoimage.impl.options.cacheBust) {
                // Cache bypass so we dont have CORS issues with cached images
                // Source: https://developer.mozilla.org/en/docs/Web/API/XMLHttpRequest/Using_XMLHttpRequest#Bypassing_the_cache
                url += ((/\?/).test(url) ? "&" : "?") + (new Date()).getTime();
            }

            return new Promise(function (resolve) {
                var request = new XMLHttpRequest();

                request.onreadystatechange = done;
                request.ontimeout = timeout;
                request.responseType = 'blob';
                request.timeout = TIMEOUT;
                request.open('GET', url, true);
                request.send();

                var placeholder;
                if(domtoimage.impl.options.imagePlaceholder) {
                    var split = domtoimage.impl.options.imagePlaceholder.split(/,/);
                    if(split && split[1]) {
                        placeholder = split[1];
                    }
                }

                function done() {
                    if (request.readyState !== 4) return;

                    if (request.status !== 200) {
                        if(placeholder) {
                            resolve(placeholder);
                        } else {
                            fail('cannot fetch resource: ' + url + ', status: ' + request.status);
                        }

                        return;
                    }

                    var encoder = new FileReader();
                    encoder.onloadend = function () {
                        var content = encoder.result.split(/,/)[1];
                        resolve(content);
                    };
                    encoder.readAsDataURL(request.response);
                }

                function timeout() {
                    if(placeholder) {
                        resolve(placeholder);
                    } else {
                        fail('timeout of ' + TIMEOUT + 'ms occured while fetching resource: ' + url);
                    }
                }

                function fail(message) {
                    console.error(message);
                    resolve('');
                }
            });
        }

        function dataAsUrl(content, type) {
            return 'data:' + type + ';base64,' + content;
        }

        function escape(string) {
            return string.replace(/([.*+?^${}()|\[\]\/\\])/g, '\\$1');
        }

        function delay(ms) {
            return function (arg) {
                return new Promise(function (resolve) {
                    setTimeout(function () {
                        resolve(arg);
                    }, ms);
                });
            };
        }

        function asArray(arrayLike) {
            var array = [];
            var length = arrayLike.length;
            for (var i = 0; i < length; i++) array.push(arrayLike[i]);
            return array;
        }

        function escapeXhtml(string) {
            return string.replace(/#/g, '%23').replace(/\n/g, '%0A');
        }

        function width(node) {
            var leftBorder = px(node, 'border-left-width');
            var rightBorder = px(node, 'border-right-width');
            return node.scrollWidth + leftBorder + rightBorder;
        }

        function height(node) {
            var topBorder = px(node, 'border-top-width');
            var bottomBorder = px(node, 'border-bottom-width');
            return node.scrollHeight + topBorder + bottomBorder;
        }

        function px(node, styleProperty) {
            var value = window.getComputedStyle(node).getPropertyValue(styleProperty);
            return parseFloat(value.replace('px', ''));
        }
    }

    function newInliner() {
        var URL_REGEX = /url\(['"]?([^'"]+?)['"]?\)/g;

        return {
            inlineAll: inlineAll,
            shouldProcess: shouldProcess,
            impl: {
                readUrls: readUrls,
                inline: inline
            }
        };

        function shouldProcess(string) {
            return string.search(URL_REGEX) !== -1;
        }

        function readUrls(string) {
            var result = [];
            var match;
            while ((match = URL_REGEX.exec(string)) !== null) {
                result.push(match[1]);
            }
            return result.filter(function (url) {
                return !util.isDataUrl(url);
            });
        }

        function inline(string, url, baseUrl, get) {
            return Promise.resolve(url)
                .then(function (url) {
                    return baseUrl ? util.resolveUrl(url, baseUrl) : url;
                })
                .then(get || util.getAndEncode)
                .then(function (data) {
                    return util.dataAsUrl(data, util.mimeType(url));
                })
                .then(function (dataUrl) {
                    return string.replace(urlAsRegex(url), '$1' + dataUrl + '$3');
                });

            function urlAsRegex(url) {
                return new RegExp('(url\\([\'"]?)(' + util.escape(url) + ')([\'"]?\\))', 'g');
            }
        }

        function inlineAll(string, baseUrl, get) {
            if (nothingToInline()) return Promise.resolve(string);

            return Promise.resolve(string)
                .then(readUrls)
                .then(function (urls) {
                    var done = Promise.resolve(string);
                    urls.forEach(function (url) {
                        done = done.then(function (string) {
                            return inline(string, url, baseUrl, get);
                        });
                    });
                    return done;
                });

            function nothingToInline() {
                return !shouldProcess(string);
            }
        }
    }

    function newFontFaces() {
        return {
            resolveAll: resolveAll,
            impl: {
                readAll: readAll
            }
        };

        function resolveAll() {
            return readAll(document)
                .then(function (webFonts) {
                    return Promise.all(
                        webFonts.map(function (webFont) {
                            return webFont.resolve();
                        })
                    );
                })
                .then(function (cssStrings) {
                    return cssStrings.join('\n');
                });
        }

        function readAll() {
            return Promise.resolve(util.asArray(document.styleSheets))
                .then(getCssRules)
                .then(selectWebFontRules)
                .then(function (rules) {
                    return rules.map(newWebFont);
                });

            function selectWebFontRules(cssRules) {
                return cssRules
                    .filter(function (rule) {
                        return rule.type === CSSRule.FONT_FACE_RULE;
                    })
                    .filter(function (rule) {
                        return inliner.shouldProcess(rule.style.getPropertyValue('src'));
                    });
            }

            function getCssRules(styleSheets) {
                var cssRules = [];
                styleSheets.forEach(function (sheet) {
                    try {
                        util.asArray(sheet.cssRules || []).forEach(cssRules.push.bind(cssRules));
                    } catch (e) {
                        console.log('Error while reading CSS rules from ' + sheet.href, e.toString());
                    }
                });
                return cssRules;
            }

            function newWebFont(webFontRule) {
                return {
                    resolve: function resolve() {
                        var baseUrl = (webFontRule.parentStyleSheet || {}).href;
                        return inliner.inlineAll(webFontRule.cssText, baseUrl);
                    },
                    src: function () {
                        return webFontRule.style.getPropertyValue('src');
                    }
                };
            }
        }
    }

    function newImages() {
        return {
            inlineAll: inlineAll,
            impl: {
                newImage: newImage
            }
        };

        function newImage(element) {
            return {
                inline: inline
            };

            function inline(get) {
                if (util.isDataUrl(element.src)) return Promise.resolve();

                return Promise.resolve(element.src)
                    .then(get || util.getAndEncode)
                    .then(function (data) {
                        return util.dataAsUrl(data, util.mimeType(element.src));
                    })
                    .then(function (dataUrl) {
                        return new Promise(function (resolve, reject) {
                            element.onload = resolve;
                            element.onerror = reject;
                            element.src = dataUrl;
                        });
                    });
            }
        }

        function inlineAll(node) {
            if (!(node instanceof Element)) return Promise.resolve(node);

            return inlineBackground(node)
                .then(function () {
                    if (node instanceof HTMLImageElement)
                        return newImage(node).inline();
                    else
                        return Promise.all(
                            util.asArray(node.childNodes).map(function (child) {
                                return inlineAll(child);
                            })
                        );
                });

            function inlineBackground(node) {
                var background = node.style.getPropertyValue('background');

                if (!background) return Promise.resolve(node);

                return inliner.inlineAll(background)
                    .then(function (inlined) {
                        node.style.setProperty(
                            'background',
                            inlined,
                            node.style.getPropertyPriority('background')
                        );
                    })
                    .then(function () {
                        return node;
                    });
            }
        }
    }
})(this);

(function () {
    'use strict';

    angular.module('su-avaliacao', [
        'su-componentes',
        'ciMapaAvaliacaoDirective']);

})();
(function () {
    'use strict';
    angular
        .module('su-avaliacao')
        .service('obterTerrenosIntersedidos', ["$http", "urlBase", function ($http, urlBase) {
            this.listarTerrenosIntersedidos = function (terreno) {
                return $http.post(urlBase + 'imovel/buscarTerrenos', terreno);
            };
        }])
        .factory('Imovel', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/cadastro/:id', { id: '@id' });
        }])
        .factory('Homologado', ["$resource", "urlBase", "$http", function ($resource, urlBase, $http) {
            var _resources = {
                homologar: $resource(
                    urlBase + 'imovel/homologado/:id', { id: '@id' }, {
                    save: { method: 'POST' },
                }
                ),
            };

            function recusar(imovelRecusado) {
                return $http.post(
                    urlBase + 'imovel/recusar-homologacao',
                    imovelRecusado
                );
            }

            return {
                save: function ($resource) {
                    return _resources.homologar.save($resource).$promise;
                },
                recusar: recusar,
            };
        }])

        .factory('Destinacao', ["$resource", "urlBase", function ($resource, urlBase) {
            var _resources = {
                enviar: $resource(
                    urlBase + 'comum/enviar-dados-destinacao/:id', { id: '@id' }, {
                    send: { method: 'POST' },
                }
                ),
            };

            return {
                send: function ($resource) {
                    return _resources.enviar.send($resource).$promise;
                },
            };
        }])
        .factory('ReceitaService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'receita/pessoa/porCpfCnpj/:cpfCnpj', {
                cpfCnpj: '@cpfCnpj',
            });
        }])
        .factory('CidadesExtService', ["$resource", "$http", "urlBase", function ($resource, $http, urlBase) {
            return $resource(urlBase + 'imovel/cidadeExterior/por-pais/:id_pais', {
                id_pais: '@id_pais',
            });
        }])
        .factory('RipsFusao', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/buscaRipsFusao/:id_imovel', {
                id_imovel: '@id_imovel',
            });
        }])
        .factory('ectService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'ect/porCep/:cep', { cep: '@cep' });
        }])
        .factory('ectCepService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'ect/localidade/porCep/:cep', { cep: '@cep' });
        }])
        .factory('ValidaPontoImovelService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/validaPontoImovel');
        }])
        .factory('CertidaoCartorial', ["$resource", "urlBase", function ($resource, urlBase) {
            function formDataObject(data) {
                var fd = new FormData();
                angular.forEach(data, function (value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + 'imovel/certidao-cartorial/upload', null, {
                save: {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {
                        'Content-Type': undefined,
                        enctype: 'multipart/form-data',
                    },
                },
            });
        }])
        .factory('PlantaUnidade', ["$resource", "urlBase", function ($resource, urlBase) {
            function formDataObject(data) {
                var fd = new FormData();
                angular.forEach(data, function (value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + 'imovel/planta-unidade/upload', null, {
                save: {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {
                        'Content-Type': undefined,
                        enctype: 'multipart/form-data',
                    },
                },
            });
        }])
        .factory('ImagemUpload', ["$resource", "urlBase", function ($resource, urlBase) {
            function formDataObject(data) {
                var fd = new FormData();
                angular.forEach(data, function (value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + 'imovel/galeria-imagem/upload', null, {
                save: {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {
                        'Content-Type': undefined,
                        enctype: 'multipart/form-data',
                    },
                },
            });
        }])

        .factory('ArquivoContratoUpload', ["$resource", "urlBase", function ($resource, urlBase) {
            function formDataObject(data) {
                var fd = new FormData();
                angular.forEach(data, function (value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + 'imovel/contrato/upload', null, {
                save: {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {
                        'Content-Type': undefined,
                        enctype: 'multipart/form-data',
                    },
                },
            });
        }])
        .factory('ExibirCertidao', ["$http", "urlBase", function ($http, urlBase) {
            function ab2str(arraybuffer) {
                var uarr = new Uint8Array(arraybuffer);
                var strings = [],
                    chunksize = 0xffff;
                var len = uarr.length;
                for (var i = 0; i * chunksize < len; i++) {
                    strings.push(
                        String.fromCharCode.apply(
                            null,
                            uarr.subarray(i * chunksize, (i + 1) * chunksize)
                        )
                    );
                }
                return strings.join('');
            }

            function setMime(string) {
                if (string.indexOf('PDF') !== -1) {
                    return 'application/pdf';
                } else if (string.indexOf('PNG') !== -1) {
                    return 'image/png';
                } else {
                    return 'image/jpg';
                }
            }

            return {
                visualizarCertidao: function (id) {
                    return $http({
                        url: 'http://' +
                            window.location.host +
                            urlBase +
                            'imovel/certidao-cartorial/listar',
                        method: 'GET',
                        responseType: 'arraybuffer',
                        params: { id: id },
                    }).then(function (data) {
                        var str = ab2str(data.data);
                        var file = new Blob([data.data], { type: setMime(str) });
                        var fileURL = URL.createObjectURL(file);
                        window.open(fileURL);
                    });
                },
            };
        }])
        .factory('ExibirPlantaUnidade', ["$http", "urlBase", function ($http, urlBase) {
            function ab2str(arraybuffer) {
                var uarr = new Uint8Array(arraybuffer);
                var strings = [],
                    chunksize = 0xffff;
                var len = uarr.length;
                for (var i = 0; i * chunksize < len; i++) {
                    strings.push(
                        String.fromCharCode.apply(
                            null,
                            uarr.subarray(i * chunksize, (i + 1) * chunksize)
                        )
                    );
                }
                return strings.join('');
            }

            function setMime(string) {
                if (string.indexOf('PDF') !== -1) {
                    return 'application/pdf';
                } else if (string.indexOf('PNG') !== -1) {
                    return 'image/png';
                } else {
                    return 'image/jpg';
                }
            }

            return {
                visualizarPlanta: function (id) {
                    return $http({
                        url: 'http://' +
                            window.location.host +
                            urlBase +
                            'imovel/planta-unidade/listar',
                        method: 'GET',
                        responseType: 'arraybuffer',
                        params: { id: id },
                    }).success(function (data) {
                        var str = ab2str(data);
                        var file = new Blob([data], { type: setMime(str) });
                        var fileURL = URL.createObjectURL(file);
                        window.open(fileURL);
                    });
                },
            };
        }])

        .factory('CancelarBenfeitoria', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(
                urlBase + 'imovel/cancelar-benfeitoria', { benfeitoria: '@benfeitoria' }, {
                save: {
                    method: 'POST',
                    params: {
                        benfeitoria: '@benfeitoria',
                    },
                },
            }
            );
        }])

        .factory('PontoImovel', ["$resource", "urlBase", function ($resource, urlBase) {
            var _resources = {
                homologar: $resource(
                    urlBase + 'imovel/ponto/validar', {}, {
                    save: { method: 'POST' },
                }
                ),
            };
            return {
                save: function ($resource) {
                    return _resources.homologar.save($resource).$promise;
                },
            };
        }]
        
        )

        .factory('ImovelCookieStorage', ["localStorageService", function (localStorageService) {
              function getImovelCookie() {
                    return localStorageService.get('pontoImovel')
              }
  
              return {
                getImovelCookie: getImovelCookie
              };


        }])

        .factory('PesquisarImovelMatricula', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(
                urlBase +
                'imovel/pesquisar-imovel-matricula/:numeroMatricula/:idCartorio', { numeroMatricula: '@numeroMatricula', idCartorio: '@idCartorio' }, {
                getImovelMatricula: {
                    method: 'GET',
                    isArray: true,
                },
            }
            );
        }])

        .factory('ImovelModel', ["$resource", function ($resource) {
            var vm = this;
            vm.imovel = {};
            vm.dadosPonto = {};
            vm.point = null;

            return {
                imovel: vm.imovel,
                dadosPonto: vm.dadosPonto,
                point: vm.point,
            };
        }])

        .factory('ImovelModel', ["$resource", function ($resource) {
            var tipoTerreno;

            function setTipoTerreno(tipoTerreno) {
                this.tipoTerreno = tipoTerreno;
            }

            function getTipoTerreno() {
                return this.tipoTerreno;
            }

            return {
                setTipoTerreno: setTipoTerreno,
                getTipoTerreno: getTipoTerreno,
            };
        }])
        .factory('PesquisaImovel', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(
                urlBase + 'imovel/pesquisar', {
                rip: '@rip',
                idPais: '@idPais',
                dataInicio: '@dataInicio',
                dataFim: '@dataFim',
                uf: '@uf',
                municipio: '@municipio',
                bairro: '@bairro',
                cidadeExterior: '@cidadeExterior',
                numeroPagina: '@numeroPagina',
                maximoPorPagina: '@maximoPorPagina',
                campoOrdenacao: '@campoOrdenacao',
            }, {
                query: {
                    method: 'GET',
                    params: {
                        rip: '@rip',
                        idPais: '@idPais',
                        dataInicio: '@dataInicio',
                        dataFim: '@dataFim',
                        uf: '@uf',
                        municipio: '@municipio',
                        bairro: '@bairro',
                        cidadeExterior: '@cidadeExterior',
                        numeroPagina: '@numeroPagina',
                        maximoPorPagina: '@maximoPorPagina',
                        campoOrdenacao: '@campoOrdenacao',
                    },
                },

                downloadFile: {
                    method: 'GET',
                    responseType: 'arraybuffer',
                    url: urlBase + 'imovel/pesquisa/relatorio',
                    params: {
                        rip: '@rip',
                        pais: '@pais',
                        dataInicio: '@dataInicio',
                        dataFim: '@dataFim',
                        uf: '@uf',
                        municipio: '@municipio',
                        bairro: '@bairro',
                        cidadeExterior: '@cidadeExterior',
                        tipo: '@tipo',
                    },
                },
            }
            );
        }])
        .factory('Documento', ["$resource", "urlBase", function ($resource, urlBase) {
            function formDataObject(data) {
                var fd = new FormData();
                angular.forEach(data, function (value, key) {
                    fd.append(key, value);
                });
                return fd;
            }
            return $resource(urlBase + 'imovel/documento/upload', null, {
                save: {
                    method: 'POST',
                    transformRequest: formDataObject,
                    headers: {
                        'Content-Type': undefined,
                        enctype: 'multipart/form-data',
                    },
                },
            });
        }])
        .factory('PesquisaImovelRelatorio', ["$http", "urlBase", function ($http, urlBase) {
            return {
                downloadFile: function (imovelPesquisa, tipo) {
                    return $http
                        .get(urlBase + 'imovel/pesquisa/relatorio', {
                            responseType: 'arraybuffer',
                            params: { imovelPesquisa: imovelPesquisa, tipo: tipo },
                        })
                        .then(function (response) {
                            return response;
                        });
                },
            };
        }])
        .factory('ValidaRipFracionamentoService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/rip/validar/:rip', { rip: '@rip' });
        }])
        .factory('ValidaRipFusaoService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/rip/validar/fusao/:rip', {
                rip: 'rip',
            });
        }])
        .factory('ImovelTerrenoService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(
                urlBase + 'imovel/imoveis/:uuidTerreno', { uuidTerreno: 'uuidTerreno' }, {
                getImoveisByUuidTerrenoService: {
                    method: 'GET',
                    params: {
                        uuidTerreno: '@uuidTerreno',
                    },
                },
            }
            );
        }])
        .factory('ImovelOverlayService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/imoveis-overlay/:uuid', {
                uuid: '@uuid',
            });
        }])

        .factory('HistoricoImovelService', ["$http", "urlBase", function ($http, urlBase) {
            return {
                getHistorico: function (uuid) {
                    return $http
                        .get(urlBase + 'historico/' + uuid)
                        .then(function (response) {
                            return response;
                        });
                },
            };
        }])
        .factory('SistemaService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/sistema/all');
        }])
        .factory('ImovelRecusadoService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/recusado/all');
        }])
        .factory('AcaoExecutasService', ["$resource", "urlBase", function ($resource, urlBase) {
            return $resource(urlBase + 'imovel/acaoExecutada/all');
        }])
        .factory('PendenciasService', ["$resource", "urlBase", function ($resource, urlBase) {
            function getResumoPendencias(tipoTerreno) {
                return $resource(urlBase + 'servico/buscaPendenciasIncorporacao');
            }

            function getPendenciaPorImovel(id) {
                return $resource(urlBase + 'imovel/pendencias/' + id);
            }

            return {
                getResumoPendencias: getResumoPendencias,
                getPendenciaPorImovel: getPendenciaPorImovel,
            };
        }]);
})();

(function () {
    'use strict';
    /*global angular*/
    angular.module('ciMapaAvaliacaoDirective', []);
}());
(function(){
  angular.module('ciMapaAvaliacaoDirective')
  .factory('NotificadorService', ["$rootScope", function($rootScope) {


    return {
        subscribe: function(scope, callback, eventName) {
            var handler = $rootScope.$on(eventName, callback);
            scope.$on('$destroy', handler);
        },

        notify: function(scope,object, eventName) {
            $rootScope.$emit(eventName,scope,object);
        },
    };
}]);


})();

(function () {
    'use strict';
    /*global angular*/
angular.module("ciMapaAvaliacaoDirective")
    .factory("PontosServiceAvaliacao", ["$resource", function($resource){
        return $resource('avaliacao/api/ponto/pontos');
    }])
    .factory("TerrenoCadastralServiceAvaliacao", ["$resource", function($resource){
        return $resource('avaliacao/api/ponto/terrenos-cadastrais');
    }])
    .factory("TerrenoCartorialServiceAvaliacao", ["$resource", function($resource){
        return $resource('avaliacao/api/ponto/terrenos-cartoriais');
    }])
    .factory("TerrenoBenfeitoriaServiceAvaliacao", ["$resource", function($resource){
        return $resource('avaliacao/api/ponto/benfeitorias');
    }])
    .factory("PontosUnidadeAutonomaServiceAvaliacao", ["$resource", function($resource){
        return $resource('avaliacao/api/ponto/pontos-unidade-autonoma');
    }]);
}());

(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('ciMapaAvaliacaoDirective')
    .factory('ciMapaAvaliacaoViewFactory', ["MAPA_CONSTANTS", function (MAPA_CONSTANTS) {

        var view,
            init;

        init = function () {

            view = new ol.View({
                center: ol.proj.fromLonLat(MAPA_CONSTANTS.GEOMETRY.GEOMETRIA_INICIAL),
                zoom: 14,
                maxZoom: 19,
                minZoom: 1
            });
        };

        init();

        return {
            getView: function () {
                return view;
            },
            getView2: function (ponto) {
                view = new ol.View({
                    center: ol.proj.fromLonLat(ponto.coordinates, 'EPSG:4326'),
                    zoom: 14,
                    maxZoom: 19,
                    minZoom: 1
                });
                return view;
            },

            centralizarMapa: function (geometry, size) {
                view.fit(geometry, size);
            },

            init: init
        };
    }]);
}());

(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('ciMapaAvaliacaoDirective')
    .factory('ciMapaValidacaoFactory', function () {

        var geoJson = {};

        var vm = this,
        validaCepPontoMunicipio;

        validaCepPontoMunicipio = function (geomMunicipio, coordPont){

            geoJson.type = 'FeatureCollection';
            geoJson.crs = {
                      'type': 'name',
                      'properties': {
                        'name': 'EPSG:3857'
                      }};

            var response;
            if(geomMunicipio.coordinates != undefined){
                geoJson.features = [{
                    'type': 'Feature',
                    'geometry': {"type":"MultiPolygon",
                    'coordinates': geomMunicipio.coordinates}
                }];

                var feature = (new ol.format.GeoJSON()).readFeatures(geoJson);
                var geometria = feature[0].getGeometry();
                if(geometria.intersectsCoordinate(coordPont)){
                    response = true;
                }else{
                    response = false;
                }
            }
            return response;
        }

        return {
            validaCepPontoMunicipio: validaCepPontoMunicipio
        }

        criaPontoPorCoordenada = function(coordenadas){
            return ponto;
        }

    });
}());

(function () {
    "use strict";
    /*global ol*/
    angular.module("ciMapaAvaliacaoDirective")
    .factory("ciMapaAvaliacaoStyleFactory", function (){

        function PinCinzaUA() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-cinza-32-UA.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                })
            });
        }

        function PinCinza() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-cinza-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                })
            });
        }

        function PinPreto() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pinPreto.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                })
            });
        }

        function PinAmarelo() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-amarelo-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function PinVermelho() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-vermelho-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function PinVerde() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-verde-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function TerrenoCinza() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(171, 171, 171, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: 'rgba(171, 171, 171, 1)',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoCinzaEscuro() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(100, 100, 100, 0.5)'
                }),
                stroke: new ol.style.Stroke({
                    color: 'rgba(120, 120, 120, 1)',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoAmarelo() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(255, 222, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#ffde00',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoAmareloPontilhado() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(255, 222, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#ffde00',
                    width: 3,
                    lineDash: [4]
                }),
                zIndex: 1
            });
        }

        function TerrenoVerde() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(20, 236, 0, 0.1)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#14ec00',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function BenfeitoriaVerde() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(20, 236, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#14ec00',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoVerdePontilhado() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(20, 236, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#14ec00',
                    width: 3,
                    lineDash: [4]
                }),
                zIndex: 1
            });
        }

        function TerrenoVermelho() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(255, 0, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#ff0000',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoRoxo() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: '#6c009e'
                }),
                stroke: new ol.style.Stroke({
                    color: '#6c009e'
                }),
                zIndex: 1
            });
        }

        function CirculoCinza() {
            return new ol.style.Style({
                image: new ol.style.Circle({
                    radius: 15,
                    stroke: new ol.style.Stroke({
                        color: '#fff'
                    }),
                    fill: new ol.style.Fill({
                        color: '#777777'
                    })
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    })
                })
            });
        }


        return {
            pinCinza: function () {
                return new PinCinza();
            },
            pinPreto: function () {
                return new PinPreto();
            },
            pinCinzaUA: function () {
                return new PinCinzaUA();
            },
            pinAmarelo: function () {
                return new PinAmarelo();
            },
            pinVermelho: function () {
                return new PinVermelho();
            },
            pinVerde: function () {
                return new PinVerde();
            },
            terrenoCinza: function () {
                return new TerrenoCinza();
            },
            terrenoCinzaEscuro: function () {
                return new TerrenoCinzaEscuro();
            },
            terrenoAmarelo: function () {
                return new TerrenoAmarelo();
            },
            terrenoAmareloPontilhado: function () {
                return new TerrenoAmareloPontilhado();
            },
            terrenoVerde: function () {
                return new TerrenoVerde();
            },
            terrenoVerdePontilhado: function () {
                return new TerrenoVerdePontilhado();
            },
            terrenoVermelho: function () {
                return new TerrenoVermelho();
            },
            terrenoRoxo: function () {
                return new TerrenoRoxo();
            },
            circuloCinza: function () {
                return new CirculoCinza();
            },
            benfeitoriaVerde: function () {
                return new BenfeitoriaVerde();
            }

        }
    });
})();

(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('ciMapaAvaliacaoDirective')
    .factory('ciMapaAvaliacaoLayersFactory', ["PontosServiceAvaliacao", "TerrenoBenfeitoriaServiceAvaliacao", "ciMapaAvaliacaoStyleFactory", "PontosUnidadeAutonomaServiceAvaliacao", "comumAvaliacaoService", function (PontosServiceAvaliacao, TerrenoBenfeitoriaServiceAvaliacao, ciMapaAvaliacaoStyleFactory, PontosUnidadeAutonomaServiceAvaliacao, comumAvaliacaoService ) {

        var vm = this,
            init,
            camadasMapa,
            camadasMinimapa,
            osm,
            bingMapa,
            bingMinimapa,
            addGeoServerTileLayer,
            addGeoServerVectorLayer,
            ortofotos,
            sourcesGeoServer = [],
            getGeoServerTileWMSSource,
            getGeoServerVectorSource,
            setPontoImovelLayer,
            setPontoUnidadeAutonomaLayer,
            clusterSource,
            styleCache,
            clusters,
            geoServerTileWMS = [],
            setPontoImovelSource,
            setPontoUnidadaAutonomaSource,
            styles = ciMapaAvaliacaoStyleFactory,
            terrenoCadastralSource,
            terrenoCartorialSource,
            terrenoBenfeitoriaSource,
            setTerrenoCadastralSource,
            setTerrenoCartorialSource,
            setTerrenoBenfeitoriaSource,
            urlGeoWebCache,
            urlGeoServer,
            pontoImovelSource,
            pontoUnidadaAutonomaSource;

        init = function () {
            camadasMapa = new ol.Collection();
            camadasMinimapa = new ol.Collection();
/*             comumAvaliacaoService.buscarUrlGeoWebCache().then(function (retorno) {
                 urlGeoWebCache = retorno.data.url;
                 vm.setOrtofotoLayer();
             });
            comumAvaliacaoService.buscarUrlGeoServer().then(function (retorno) {
                 urlGeoServer = retorno.data.url;
                 getGeoServerTileWMSSource();
                 getGeoServerVectorSource();
                 setPontoImovelSource();
                 setPontoUnidadaAutonomaSource();
             }); */
            vm.setOsm();
            vm.setBingMapa();
            vm.setBingMinimapa();
        };
        geoServerTileWMS = [
            {
                name: "UF",
                layer: "ide:uf",
                label: "UF",
                visible: true,
                required: true
            },
            {
                name: "municipio",
                layer: "ide:municipio",
                label: "Município",
                visible: true,
                required: true
            },
            {
                name: "areaUniao",
                layer: "ide:tb_areas_uniao",
                label: "Área da União",
                visible: false,
                required: false
            }
        ];

        vm.setOsm = function () {
            osm = new ol.layer.Tile({
                source: new ol.source.OSM(),
                name: 'osm',
                required: true
            });
            camadasMapa.push(osm);
            camadasMinimapa.push(osm);
        };

        vm.setBingMapa = function () {
            bingMapa =  new ol.layer.Tile({
                source: new ol.source.BingMaps({
                    key: 'AtwyeZCHS5j5qQT3JNFayXjp_8FvRtPWefOsSW5g6MFzMgPIRvzLt5BhfsDnppQ9',
                    imagerySet: 'AerialWithLabels'
                }),
                visible: false,
                name: 'bingMapa',
                required: true
            });
            camadasMapa.push(bingMapa);
        };

        vm.setBingMinimapa = function () {
            bingMinimapa =  new ol.layer.Tile({
                source: new ol.source.BingMaps({
                    key: 'AtwyeZCHS5j5qQT3JNFayXjp_8FvRtPWefOsSW5g6MFzMgPIRvzLt5BhfsDnppQ9',
                    imagerySet: 'AerialWithLabels'
                }),
                visible: true,
                name: 'bingMinimapa',
                required: true
            });
            camadasMinimapa.push(bingMinimapa);
        };
        vm.setOrtofotoLayer = function () {

            ortofotos = new ol.layer.Tile({
                source: new ol.source.TileWMS({
                    url: urlGeoWebCache,
                    params: {
                        'layers': 'layerGroupBrasil',
                        'srs': 'EPSG:900913',
                        'styles': 'ortofoto',
                        'TILED': true,
                        'format': 'image/png',
                        'transparent': false,
                        'BBOX': '0.0, 0.0, -1.001875417E7, -1.001875417E7'
                    }
                }),
                name: 'ortofotos',
                visible: false,
                label: 'Ortofotos e Ortofotocartas',
                required: false
            });
            camadasMapa.push(ortofotos);
        };

        addGeoServerTileLayer = function () {
            var i,
                layer;
            for (i = 0; i < geoServerTileWMS.length; i++) {
                layer = new ol.layer.Tile({
                    source: sourcesGeoServer[geoServerTileWMS[i].name],
                    name: geoServerTileWMS[i].name,
                    visible: geoServerTileWMS[i].visible,
                    label: geoServerTileWMS[i].label,
                    required: geoServerTileWMS[i].required
                });

                camadasMapa.push(layer);
            }
        };

        addGeoServerVectorLayer = function (name, source, label, visible, required) {
            var layer = new ol.layer.Vector({
                source: source,
                style: function (feature) {
                    if (feature.getProperties().unidadeAutonoma) {
                        if (name === "benfeitoria") {
                            return styles.terrenoVerde();
                        } else {
                            return styles.terrenoCinzaEscuro();
                        }
                    } else {
                        return styles.terrenoCinza();
                    }
                },
                name: name,
                label: label,
                visible: visible,
                required: required
            });
            camadasMapa.push(layer);
        };

        setPontoImovelSource = function () {
            pontoImovelSource = new ol.source.Vector();
            setPontoImovelLayer();
            PontosServiceAvaliacao.get(function(result) {
                var features = (new ol.format.GeoJSON({
                        defaultProjection: 'EPSG:3857'
                    })).readFeatures(result);
                pontoImovelSource.addFeatures(features);
            });
        };

        setPontoUnidadaAutonomaSource = function () {
            pontoUnidadaAutonomaSource = new ol.source.Vector();
            setPontoUnidadeAutonomaLayer();
            PontosUnidadeAutonomaServiceAvaliacao.get(function(result) {
                var features = (new ol.format.GeoJSON({
                    defaultProjection: 'EPSG:3857'
                })).readFeatures(result);
                pontoUnidadaAutonomaSource.addFeatures(features);
            });
        };

        getGeoServerTileWMSSource = function () {
            for (var i = 0; i < geoServerTileWMS.length; i++) {
                sourcesGeoServer[geoServerTileWMS[i].name] = new ol.source.TileWMS({
                    url: urlGeoServer,
                    params: {
                        'layers': geoServerTileWMS[i].layer,
                        'srs': 'EPSG:4674',
                        'TILED': true
                    }
                });
            }
            addGeoServerTileLayer();
        };

        getGeoServerVectorSource = function () {
            terrenoCadastralSource = new ol.source.Vector();
            terrenoCartorialSource = new ol.source.Vector();
            terrenoBenfeitoriaSource = new ol.source.Vector();
            setTerrenoCadastralSource();
            setTerrenoCartorialSource();
            setTerrenoBenfeitoriaSource();
        };

        setTerrenoCadastralSource = function () {
            // addGeoServerVectorLayer("terrenoCadastral", terrenoCadastralSource, "Terreno Cadastral", true, false);
            // TerrenoCadastralServiceAvaliacao.get(function (result){
            //     var features = (new ol.format.GeoJSON({
            //         defaultProjection: 'EPSG:3857'
            //     })).readFeatures(result);
            //     terrenoCadastralSource.addFeatures(features);
            // });
        };

        setTerrenoCartorialSource = function () {
            // addGeoServerVectorLayer("terrenoCartorial", terrenoCartorialSource, "Terreno Cartorial", true, false);
            // TerrenoCartorialServiceAvaliacao.get(function (result) {
            //     var features = (new ol.format.GeoJSON({
            //         defaultProjection: 'EPSG:3857'
            //     })).readFeatures(result);
            //    terrenoCartorialSource.addFeatures(features);
            // });
        };

        setTerrenoBenfeitoriaSource = function () {
            addGeoServerVectorLayer("benfeitoria", terrenoBenfeitoriaSource, "Benfeitoria", true, false);
            TerrenoBenfeitoriaServiceAvaliacao.get(function (result) {
                var features = (new ol.format.GeoJSON({
                    defaultProjection: 'EPSG:3857'
                })).readFeatures(result);
                terrenoBenfeitoriaSource.addFeatures(features);
            });
        };

        setPontoImovelLayer = function () {
            clusterSource = new ol.source.Cluster({
                distance: 70,
                source: pontoImovelSource,
                projection: "EPSG:3857"
            });
            styleCache = [];
            clusters = new ol.layer.Vector({
                source: clusterSource,
                style: function (feature) {
                    var size = feature.get('features').length;
                    var style = styleCache[size];
                    if(!style){
                        style = styles.pinCinza();
                        if (size > 1) {
                            style = styles.circuloCinza();
                            style.getText().setText(size.toString());
                        } else {
                            style.getText().setText("");
                        }
                        styleCache[size] = style;
                    }
                    return style;
                },
                name: "pontoImovel",
                label: "Ponto do Imóvel",
                required: false,
                visible: true
            });
           camadasMapa.push(clusters);
        };

        setPontoUnidadeAutonomaLayer = function () {
           var clusterUnidadeAutonomaSource = new ol.source.Cluster({
                distance: 70,
                source: pontoUnidadaAutonomaSource,
                projection: "EPSG:3857"
            });
            var styleCacheUA = [];
            var clusterLayer = new ol.layer.Vector({
                source: clusterUnidadeAutonomaSource,
                style: function (feature) {
                    var size = feature.get('features').length;
                    var style = styleCacheUA[size];
                    if(!style){
                        style = styles.pinCinzaUA();
                        if (size > 1) {
                            style.getText().setText(size.toString());
                        } else {
                            style.getText().setText("");
                        }
                        styleCacheUA[size] = style;
                    }
                    return style;
                },
                name: "pontoUnidadeAutonoma",
                label: "Ponto Unidade Autônoma",
                required: false,
                visible: true
            });
            camadasMapa.push(clusterLayer);
        };

        return {

            getCamadas: function (isMinimapa) {
                if (isMinimapa) {
                    return camadasMinimapa;
                } else {
                    return camadasMapa;
                }
            },

            init: init
        };
    }]);
}());

(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('su-avaliacao').directive('ciMinimapaAvaliacao', ["ciMapaAvaliacaoViewFactory", "ciMapaAvaliacaoLayersFactory", "NotificadorService", "MAPA_CONSTANTS", function (ciMapaAvaliacaoViewFactory, ciMapaAvaliacaoLayersFactory, NotificadorService, MAPA_CONSTANTS) {
        return {
            scope: {
            },
            template: '<div></div>',
            link: function (scope, element, attrs) {
                angular.element(document).ready(function () {
                    var viewFac = ciMapaAvaliacaoViewFactory,
                        layersFac = ciMapaAvaliacaoLayersFactory,
                        layers = layersFac.getCamadas(true),
                        map,
                        notifiers = MAPA_CONSTANTS.NOTIFICADORES,
                        height,
                        width;

                    if (attrs.height && !isNaN(attrs.height)) {
                        height = parseInt(attrs.height, 10);
                    } else {
                        height = 100;
                    }
                    if (attrs.width && !isNaN(attrs.width)) {
                        width = parseInt(attrs.width, 10);
                    } else {
                        width = 100;
                    }

                    map = new ol.Map({
                        target: element[0].firstChild,
                        layers: layers,
                        view: viewFac.getView(),
                        controls: ol.control.defaults({
                            attribution: false,
                            zoom: false,
                            rotate: false
                        }),
                        interactions: ol.interaction.defaults({
                            mouseWheelZoom: false,
                            doubleClickZoom: false,
                            altShiftDragRotate: false,
                            shiftDragZoom: false,
                            dragPan: false
                        })
                    });

                    map.setSize([width, height]);

                    NotificadorService.subscribe(scope, function (event, scope, boolean) {
                        angular.forEach(map.getLayers(), function (value) {
                            if (value.get('name') === 'bingMinimapa') {
                                value.setVisible(!boolean);
                            }
                        });
                    }, notifiers.ALTERAR_VISIBILIDADE_BING);
                });
            }
        };
    }]);
}());

(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('su-avaliacao')
    .directive('ciMapaAvaliacao', ["ImovelCookieStorage", "$rootScope", "ciMapaAvaliacaoViewFactory", "ciMapaValidacaoFactory", "ciMapaAvaliacaoLayersFactory", "NotificadorService", "MensagensService", "MAPA_CONSTANTS", "$timeout", "$state", "$mdDialog", "$stateParams", "ciMapaAvaliacaoStyleFactory", "PontoImovel", function (ImovelCookieStorage,
        $rootScope, ciMapaAvaliacaoViewFactory, ciMapaValidacaoFactory, ciMapaAvaliacaoLayersFactory,
         NotificadorService, MensagensService, MAPA_CONSTANTS, $timeout, $state, $mdDialog, $stateParams, ciMapaAvaliacaoStyleFactory, PontoImovel) {
        return {
            templateUrl: 'scripts-avaliacao/app/directives/ci-mapa/view/ci-mapa-template.html',
            scope: {
                showMinimapa: '=showminimapa',
                heightMinimapa: '=heightminimapa',
                widthMinimapa: '=widthminimapa',
                tabIndex: '=tabindex',
                isUnidadeAutonoma: '=isunidadeautonoma',
                amostra: '=',
                action: '@',
                benfeitoria: '=',
                edificacoes: '=',
                btCamada: '=', /** Botão de camada*/
                btMarcar: '=', /** Botão para marcar ponto*/
                btDesenharPonto: '=', /** Botão desenhar ponto*/
                btDesenharUnidadeAutonoma: '=', /** Botão desenhar Unidade autonoma*/
                btDesenharBenfeitoria: '=', /** Botão desenhar benfeitoria*/
                btApagar: '=', /** Botão para apagar todos os pontos */
                btRemover: '=', /** Botão para remover ponto Marcado */
                latitude: '=', /** Variável para armazenar latitude */
                longitude: '=', /** Variável para armazenar longitude */
                visualizar: '=', /** Visualizar dados da amostra */
                detalharAmostra: '=', /** Variável bloquear conteúdo das legendas */
                listaAmostras: '=', /** Variável para armazenar a lista de amostras */
                btAdicionarAmostras: '=', /** Botão para adicionar amostras */
                cancelarAmostra: '&', /** Função para cancelar Amostras */
                editarAmostra: '&', /** Função para editar Amostras */
                visualizarAmostra: '&', /** Função para visualizar Amostras */
                consultarAdicionarAmostras: '=',
                bloquearMapa: '=', /** Habilita bloqueio do mapa até o clique */
                desabilitar: '=', /** Desabilita interações com o mapa */
                btSelecionar: '=', /** Opção de selecionar Amostra já cadastrada */
                mostrarAmostras: '=', /** Habilita a função de mostrar as amostras no mapa através do notify*/
                mapaVariaveisAmostras: '='
            },
            link: function (scope, element) {

                angular.element(document).ready(function () {
                    var vm = scope,
                        init,
                        viewFac = ciMapaAvaliacaoViewFactory,
                        layersFac = ciMapaAvaliacaoLayersFactory,
                        imovelSource = new ol.source.Vector(),
                        imovelLayer = new ol.layer.Vector({
                            source: imovelSource,
                            name: 'imovel',
                            required: true
                        }),
                        geoJson = new ol.format.GeoJSON({
                            defaultProjection: 'EPSG:4674'
                        }),
                        drawPoint,
                        drawPolygon,
                        erase,
                        setErase,
                        removerCustomInteractions,
                        setDrawPoint,
                        styles = ciMapaAvaliacaoStyleFactory,
                        notifiers = MAPA_CONSTANTS.NOTIFICADORES,
                        featureIds = MAPA_CONSTANTS.FEATURE_ID,
                        geometrias = MAPA_CONSTANTS.GEOMETRY,
                        camadas = MAPA_CONSTANTS.LAYERS,
                        tiposImovel = MAPA_CONSTANTS.TIPO_IMOVEL,
                        setDrawPolygon,
                        setFeatureSobreposicao,
                        terrenoIntersecaoStyle,
                        terrenoInvalidoStyle,
                        terrenoAmareloStyle,
                        centroSegmentoStyle,
                        adicionarMarcador,
                        validarPonto,
                        mapClickEvent,
                        removerFeaturesTerrenoSobrepostoCadastral,
                        removerFeaturesTerrenoSobrepostoCartorial,
                        removerFeaturesCentroSegmento,
                        terrenoCartorialValidoStyle,
                        adicionarCentroSegmentos,
                        setClickEvent,
                        removerFeatureById,
                        validarGeometriaTerreno,
                        drawPolygonBenfeitoria,
                        setDrawPolygonBenfeitoria,
                        terrenoDefaultStyle,
                        overlayBenfeitoria,
                        getBenfeitoriaByCodigo,
                        getElementHTMLOverlay,
                        destacarBenfeitoria,
                        imovel = scope.imovel,
                        terrenoImovel,
                        geometriaImovelTerrenoCadastralJson,
                        geometriaImovelTerrenoCartorialJson,
                        geometriaImovelTerrenoCadastral,
                        geometriaImovelTerrenoCartorial,
                        geometriaImovelPontoJson,
                        geometriaImovelPonto,
                        geometriaImovelUnidadeAutonomaJson,
                        geometriaImovelUnidadeAutonoma,
                        action = scope.action,
                        removeAndSetPonto,
                        feature = new ol.Feature({}),
                        setDrawPolygonUnidadeAutonoma,
                        drawPolygonUnidadeAutonoma,
                        desenhaPorGeometria,
                        setStyleTerrenoByFeature,
                        getImovelOverlayHTML,
                        centralizarPorTerreno,
                        removerFeature,
                        getImovelById,
                        pontoImovelValidoStyle,
                        pontoImovelInvalidoStyle,
                        terrenoValidoStyle,
                        terrenoValidoPontilhadoStyle,
                        unidadeAutonomaStyle,
                        benfeitoriaStyle,
                        benfeitoriaDestacadaStyle,
                        removeFeatures,
                        adicionaPontoFeatures,
                        formatarArea,
                        validarGeometriaBenfeitoria,
                        removerFeaturesUnidadeAutonoma,
                        getFeatureByUuid,
                        removeFeatureUnidadeAutonomaByNameLayer,
                        desenharGeometriaEdificacao,
                        reaproveitarDadosImovelAndEdificacoes,
                        featuresRemovidasUnidadeAutonomas = [],
                        redesenharFeaturesRemovidasUnidadeAutonoma,
                        adicionaFeatureByLayer,
                        imovelOverlay,
                        overlay_,
                        /** Overlay de amostra */
                        overlayAmostra = new ol.Overlay({
                            element: document.getElementById('overlayAmostra'),
                            autoPan: true,
                            autoPanAnimation: {
                                duration: 250
                            }
                        }),
                        amostra

                    /** Lista de Camadas inseridas no mapa*/
                    vm.layers = layersFac.getCamadas(false).getArray();

                    /** Lista de Camadas listadas na tela*/
                    vm.layersTela = layersFac.getCamadas(false).getArray();

                    vm.map = {};
                    vm.listaLayers = false;
                    vm.bloquearMapaTerreno = false;
                    vm.bloquearMapaUnidadeAutonoma = false;
                    vm.bloquearMapaBenfeitoria = false;
                    if (vm.bloquearMapa === 'undefined' || vm.bloquearMapa === '') {
                        vm.bloquearMapa = true;
                    }

                    var vmr = $rootScope;

                    init = function () {


                        /** Cria um ponto inicial ao carregar o mapa
                         *  Caso não haja latitude e longitude será adotado uma coordenada fixa aleatória
                         *  Inicio ponto inicial */
                        var pontoInicial = {
                            type: 'Point'
                        };

                        if(vm.imovel === undefined){
                            vm.imovel = ImovelCookieStorage.getImovelCookie()
                        }
                        
                        if (vm.imovel) {
                            pontoInicial = vm.imovel.pontoImovel;
                        } else if (vm.latitude && vm.longitude) {
                            pontoInicial.coordinates = [vm.latitude, vm.longitude];
                        } else {
                            pontoInicial.coordinates = [-5330442.433589233, -1780802.4390055723];
                        }
                        

                        /**  Fim ponto inicial */

                        /** Início Controles*/

                        /** Controles desabilitados para o mapa */
                        var controlDesabilitado = ol.control.defaults({
                            attribution: false,
                            rotate: false,
                            zoom: !vm.desabilitar,
                            fullscreen: !vm.desabilitar,
                            scaleline: !vm.desabilitar,
                            overviewmap: !vm.desabilitar
                        });

                        /** Controles habilitados para o mapa */
                        var controlHabilitado = ol.control.defaults({
                            attribution: false,
                            rotate: false
                        }).extend([
                            new ol.control.FullScreen({
                                tipLabel: 'Expandir Mapa',
                                className: 'btn-fullscreen',
                                label: '\u2922'
                            }),
                            new ol.control.ScaleLine(),
                            new ol.control.Attribution({
                                className: 'ol-attribution ol-attribution-custom'
                            })
                        ]);

                        var control;

                        if (vm.desabilitar) {
                            control = controlDesabilitado;
                        } else {
                            control = controlHabilitado;
                        }
                        /** Fim Controles*/

                        /** Instanciar o Mapa */
                        vm.map = new ol.Map({
                            target: element[0].firstChild,
                            view: viewFac.getView2(pontoInicial),
                            overlays: [overlayAmostra],
                            controls: control,
                            interactions: ol.interaction.defaults({
                                altShiftDragRotate: false,
                                dragPan: !vm.desabilitar, /** Navegação do mapa com clique e arraste*/
                                mouseWheelZoom: !vm.desabilitar, /** Zoom com mouse */
                                doubleClickZoom: !vm.desabilitar /** Zoom com dois cliques*/
                            }),
                            renderer: 'canvas'
                        });

                        /** Adiciona camadas no mapa*/
                        angular.forEach(vm.layers, function (layer) {
                            vm.map.addLayer(layer);
                        });

                        vm.map.getLayerGroup().getLayers().insertAt(vm.map.getLayerGroup().getLayers().getArray().length, imovelLayer);

                        /* Estilo dos pontos */
                        pontoImovelInvalidoStyle = styles.pinVermelho();

                        if (scope.tabIndex === -1) {
                            pontoImovelValidoStyle = styles.pinAmarelo();
                        } else {
                            pontoImovelValidoStyle = styles.pinVerde();
                        }

                        if (scope.action === 'edit' || scope.action == 'add') {
                              adicionarCentroSegmentos();
                              setClickEvent();
                            
                            //if ((scope.tabIndex === parseInt($stateParams.tab, 10)) || (!$stateParams.tab && scope.tabIndex === -1)) {
                            //centralizarPorTerreno();
                            //                                }
                        }

                        /* Estilo dos terrenos */
                        terrenoInvalidoStyle = styles.terrenoVermelho();
                        terrenoIntersecaoStyle = styles.terrenoRoxo();

                        if (scope.tabIndex === 1) {
                            terrenoValidoStyle = styles.terrenoAmarelo();
                            terrenoValidoPontilhadoStyle = styles.terrenoAmareloPontilhado();
                        } else {
                            terrenoValidoStyle = styles.terrenoVerde();
                            terrenoValidoPontilhadoStyle = styles.terrenoVerdePontilhado();
                        }

                        /* Estilo da unidade autônoma*/
                        if (scope.tabIndex === 3) {
                            unidadeAutonomaStyle = styles.terrenoAmarelo();
                        } else {
                            unidadeAutonomaStyle = styles.terrenoVerde();
                        }

                        /* Estilo da benfeitoria */
                        benfeitoriaDestacadaStyle = styles.terrenoAmarelo();
                        if (scope.tabIndex === 4) {
                            benfeitoriaStyle = styles.terrenoAmarelo();
                        } else {
                            benfeitoriaStyle = styles.terrenoVerde();
                        }

                        vm.map.getView().on("change:resolution", function () {
                            if (vm.map.getView().getZoom() <= 13) {
                                removerFeaturesCentroSegmento();
                            } else if (scope.tabIndex === 1) {
                                adicionarCentroSegmentos();
                            }
                        });

                        if (scope.tabIndex === 2) {
                            overlay_ = new ol.Overlay({
                                element: angular.element(document.querySelector('#overlayLocalizacao'))[0],
                                positioning: 'bottom-left'
                            });
                            vm.map.addOverlay(overlay_);
                        }

                        if (scope.tabIndex === 2) {
                            overlay_ = new ol.Overlay({
                                element: angular.element(document.querySelector('#overlayLocalizacao'))[0],
                                positioning: 'bottom-left'
                            });
                            vm.map.addOverlay(overlay_);
                        }

                        if (scope.tabIndex === 2) {
                            overlay_ = new ol.Overlay({
                                element: angular.element(document.querySelector('#overlayDadosGerais'))[0],
                                positioning: 'bottom-left'
                            });
                            vm.map.addOverlay(overlay_);
                        }

                        /** Caso a variável imóvel não esteja vazia o mapa será preenchido de acordo com o que há dentro de imóvel*/
                        if (scope.imovel) {
                            geometriaImovelPontoJson = scope.imovel.pontoImovel;
                            geometriaImovelPonto = geoJson.readGeometry(geometriaImovelPontoJson);
                            terrenoImovel = scope.imovel.terreno;
                            if (terrenoImovel) {
                                geometriaImovelTerrenoCadastralJson = terrenoImovel.geometriaTerreno;
                                geometriaImovelTerrenoCartorialJson = terrenoImovel.geometriaTerrenoCartorial;
                                geometriaImovelTerrenoCadastral = geoJson.readGeometry(geometriaImovelTerrenoCadastralJson);
                                geometriaImovelTerrenoCartorial = geoJson.readGeometry(geometriaImovelTerrenoCartorialJson);

                            }
                            if (scope.imovel.unidadeAutonoma && scope.imovel.unidadeAutonoma.edificacao && scope.imovel.unidadeAutonoma.edificacao.geometria) {
                                geometriaImovelUnidadeAutonomaJson = scope.imovel.unidadeAutonoma.edificacao.geometria;
                                geometriaImovelUnidadeAutonoma = geoJson.readGeometry(geometriaImovelUnidadeAutonomaJson);
                            }

                            if (scope.imovel.terreno.geometriaTerreno) {
                                feature = new ol.Feature({
                                    geometry: geometriaImovelTerrenoCadastral
                                });
                                feature.setId(featureIds.TERRENO_CADASTRAL);
                                if (scope.imovel.terreno.geometriaTerrenoCartorial) {
                                    feature.setStyle(terrenoValidoPontilhadoStyle);
                                } else {
                                    feature.setStyle(terrenoValidoStyle);
                                }
                                imovelSource.addFeature(feature);
                            }

                            if (scope.imovel.terreno.geometriaTerrenoCartorial) {
                                feature = new ol.Feature({
                                    geometry: geometriaImovelTerrenoCartorial
                                });
                                feature.setId(featureIds.TERRENO_CARTORIAL);
                                feature.setStyle(terrenoValidoStyle);
                                imovelSource.addFeature(feature);
                            }

                            if (scope.imovel.pontoImovel) {
                                feature = new ol.Feature({
                                    geometry: geometriaImovelPonto
                                });
                                feature.setId(featureIds.PONTO_IMOVEL);
                                feature.setStyle(pontoImovelValidoStyle);
                                imovelSource.addFeature(feature);
                            }

                            if (scope.imovel.unidadeAutonoma && scope.imovel.unidadeAutonoma.edificacao && scope.imovel.unidadeAutonoma.edificacao.geometria) {
                                feature = new ol.Feature({
                                    geometry: geometriaImovelUnidadeAutonoma
                                });
                                feature.setId(featureIds.UNIDADE_AUTONOMA);
                                feature.setStyle(unidadeAutonomaStyle);
                                imovelSource.addFeature(feature);
                            }

                            if (scope.imovel.benfeitorias && scope.imovel.benfeitorias.length > 0) {
                                angular.forEach(scope.imovel.benfeitorias, function (benfeitoria) {
                                    var feature;
                                    feature = new ol.Feature({
                                        geometry: geoJson.readGeometry(benfeitoria.geometriaBenfeitoria)
                                    });
                                    feature.setId(benfeitoria.codigo);
                                    feature.setStyle(benfeitoriaStyle);
                                    feature.set('name', 'benfeitoria');
                                    imovelSource.addFeature(feature);
                                });
                            }
                        }
                        /** Marcar amostras no mapa */
                        NotificadorService.subscribe(scope, function (event, escopo, imovel) {
                            if (imovel) {
                                geometriaImovelPontoJson = imovel.pontoImovel;
                                geometriaImovelPonto = geoJson.readGeometry(geometriaImovelPontoJson);
                                terrenoImovel = imovel.terreno;
                                if (terrenoImovel) {
                                    geometriaImovelTerrenoCadastralJson = terrenoImovel.geometriaTerreno;
                                    geometriaImovelTerrenoCartorialJson = terrenoImovel.geometriaTerrenoCartorial;
                                    geometriaImovelTerrenoCadastral = geoJson.readGeometry(geometriaImovelTerrenoCadastralJson);
                                    geometriaImovelTerrenoCartorial = geoJson.readGeometry(geometriaImovelTerrenoCartorialJson);

                                }

                                if (imovel.terreno.geometriaTerreno) {
                                    feature = new ol.Feature({
                                        geometry: geometriaImovelTerrenoCadastral
                                    });
                                    feature.setId(featureIds.TERRENO_CADASTRAL);
                                    if (imovel.terreno.geometriaTerrenoCartorial) {
                                        feature.setStyle(terrenoValidoPontilhadoStyle);
                                    } else {
                                        feature.setStyle(terrenoValidoStyle);
                                    }
                                    imovelSource.addFeature(feature);
                                }

                                if (imovel.terreno.geometriaTerrenoCartorial) {
                                    feature = new ol.Feature({
                                        geometry: geometriaImovelTerrenoCartorial
                                    });
                                    feature.setId(featureIds.TERRENO_CARTORIAL);
                                    feature.setStyle(terrenoValidoStyle);
                                    imovelSource.addFeature(feature);
                                }

                                if (imovel.pontoImovel) {
                                    feature = new ol.Feature({
                                        geometry: geometriaImovelPonto
                                    });
                                    feature.setId(featureIds.PONTO_IMOVEL);
                                    feature.setStyle(pontoImovelValidoStyle);
                                    imovelSource.addFeature(feature);
                                }

                                if (imovel.unidadeAutonoma && imovel.unidadeAutonoma.edificacao && imovel.unidadeAutonoma.edificacao.geometria) {
                                    feature = new ol.Feature({
                                        geometry: geometriaImovelUnidadeAutonoma
                                    });
                                    feature.setId(featureIds.UNIDADE_AUTONOMA);
                                    feature.setStyle(unidadeAutonomaStyle);
                                    imovelSource.addFeature(feature);
                                }

                                if (imovel.benfeitorias && imovel.benfeitorias.length > 0) {
                                    angular.forEach(imovel.benfeitorias, function (benfeitoria) {
                                        var feature;
                                        feature = new ol.Feature({
                                            geometry: geoJson.readGeometry(benfeitoria.geometriaBenfeitoria)
                                        });
                                        feature.setId(benfeitoria.codigo);
                                        feature.setStyle(benfeitoriaStyle);
                                        feature.set('name', 'benfeitoria');
                                        imovelSource.addFeature(feature);
                                    });
                                }
                            }
                        }, notifiers.MARCAR_IMOVEL);

                        /** Função que centraliza o mapa e ajusta o zoom de acordo com a posição das amostras inseridas*/
                        function centralizarPorAmostra(coordenadas) {
                            var view = vm.map.getView();
                            var coord = new ol.geom.MultiPoint(coordenadas);
                            view.fit(coord, vm.map.getSize());
                        }

                        if (vm.mostrarAmostras) {
                            /** Marcar amostras no mapa */
                            NotificadorService.subscribe(scope, function (event, escopo, amostras) {
                                if (amostras && amostras.length > 0) {
                                    imovelSource.clear();
                                    vm.amostras = angular.copy(amostras);
                                    var coordenadas = [];
                                    for (var i = 0; i < amostras.length; i++) {
                                        var geo = {
                                            coordinates: [amostras[i].latitude, amostras[i].longitude],
                                            type: 'Point'
                                        };
                                        var geometria = geoJson.readGeometry(geo);
                                        if (geometria) {
                                            var feature = new ol.Feature({
                                                geometry: geometria
                                            });
                                            feature.setId(amostras[i].id);
                                            if (amostras[i].pinAmarelo) {
                                                amostra = amostras[i];
                                                feature.setStyle(styles.pinPreto());
                                            } else {
                                                feature.setStyle(styles.pinPreto());
                                            }

                                            imovelSource.addFeature(feature);
                                        }
                                        coordenadas.push([amostras[i].latitude, amostras[i].longitude])
                                    }
                                }

                                centralizarPorAmostra(coordenadas);
                            }, notifiers.MARCAR_AMOSTRAS);
                        }

                        /** Desenhar benfeitorias no mapa */
                        NotificadorService.subscribe(scope, function (event, escopo, benfeitorias) {
                            for (var i = 0; i < benfeitorias.length; i++) {
                                if (benfeitorias[i].geometriaBenfeitoria) {
                                    var feature;
                                    feature = new ol.Feature({
                                        geometry: geoJson.readGeometry(benfeitorias[i].geometriaBenfeitoria[0]),
                                        name: 'BENFEITORIA:' + benfeitorias[i].id
                                    });
                                    feature.setId('BENFEITORIA:' + benfeitorias[i].id);
                                    feature.setStyle(styles.benfeitoriaVerde());
                                    imovelSource.addFeature(feature);
                                }
                                if (benfeitorias[i].geometriaTerreno) {
                                    var feature;

                                    feature = new ol.Feature({
                                        geometry: geoJson.readGeometry(benfeitorias[i].geometriaTerreno),
                                        name: 'TERRENO:' + benfeitorias[i].id
                                    });
                                    feature.setId('TERRENO:' + benfeitorias[i].id);
                                    feature.setStyle(terrenoValidoStyle);
                                    imovelSource.addFeature(feature);
                                }
                                if (benfeitorias[i].pontoImovel) {
                                    var feature;
                                    feature = new ol.Feature({
                                        geometry: geoJson.readGeometry(benfeitorias[i].pontoImovel),
                                        name: 'IMOVEL:' + benfeitorias[i].id
                                    });
                                    feature.setId('IMOVEL:' + benfeitorias[i].id);
                                    feature.setStyle(styles.pinVermelho());
                                    imovelSource.addFeature(feature);
                                }
                            }

                        }, notifiers.DESENHAR_BENFEITORIAS);

                        NotificadorService.subscribe(scope, function (event, scope, geometriaJson) {
                            var geometria;
                            if (geometriaJson) {
                                geometria = geoJson.readGeometry(geometriaJson);
                                viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());
                            }
                        }, notifiers.CENTRALIZAR_POR_MUNICIPIO);

                        NotificadorService.subscribe(scope, function (e, escope, geo) {

                            angular.forEach(imovelSource.getFeatures(), function (feature) {
                                var id = feature.getId();
                                if (id && typeof id === 'string') {
                                    if (id.indexOf('BENFEITORIA') || id.indexOf('TERRENO') || id.indexOf('IMOVEL')) {
                                        imovelSource.removeFeature(feature);
                                    }
                                }
                            })

                        }, notifiers.LIMPAR_BENFEITORIAS);

                        NotificadorService.subscribe(scope, function (event, scope, coordenadas) {
                            vm.map.setView(viewFac.getView2({coordinates: coordenadas, type: 'Point'}));
                        }, notifiers.CENTRALIZAR_MAPA);

                        /** Somente se a opção de visualizar amostras for TRUE */
                        if (vm.visualizar) {
                            vm.map.on('click', function (e) {
                                overlayAmostra.setPosition();
                                var features = [];
                                /** Verifica se existe alguma feature no local do clique */
                                if (vm.map.hasFeatureAtPixel(e.pixel)) {
                                    if (vm.adicionandoPonto) {
                                        return
                                    }
                                    /** Armazenas todas as features do local */
                                    vm.map.forEachFeatureAtPixel(e.pixel, function (feature) {
                                        features.push(feature);
                                    });
                                    vm.amostrasSelecionadas = [];
                                    if (features) {
                                        if (features[0].getId() == featureIds.PONTO_AMOSTRA || !features[0].getId()) {
                                            return
                                        }
                                        /** Cria uma lista de amostras selecionadas para mostrar no overlay */
                                        for (var j = 0; j < features.length; j++) {
                                            for (var i = 0; i < vm.amostras.length; i++) {
                                                if (vm.amostras[i].id === features[j].getId()) {
                                                    vm.amostrasSelecionadas.push(angular.copy(vm.amostras[i]));
                                                    isAdicionado(vm.amostras[i]);
                                                    break;
                                                }
                                            }
                                        }
                                        /** Seleciona o local onde o overlay irá abrir */
                                        $timeout(function () {
                                            overlayAmostra.setPosition(features[0].getGeometry().getCoordinates());
                                        });
                                    }
                                }
                            });
                        }
                    };

                    /** Verifica se amostra ja foi inserida */
                    function isAdicionado(amostra) {
                        if (vm.listaAmostras) {
                            for (var i = 0; i < vm.listaAmostras.length; i++) {
                                if (amostra.id === vm.listaAmostras[i].id) {
                                    amostra.adicionado = true;
                                    return
                                }
                            }
                        }
                    }

                    // Estilos de Features
                    terrenoAmareloStyle = new ol.style.Style({
                        fill: new ol.style.Fill({
                            color: 'rgba(255, 206, 0, 0.3)'
                        }),
                        stroke: new ol.style.Stroke({
                            color: 'rgba(255, 206, 0, 1)',
                            width: 3
                        }),
                        zIndex: 1
                    });

                    terrenoCartorialValidoStyle = new ol.style.Style({
                        fill: new ol.style.Fill({
                            color: 'rgba(30, 144, 255, 0.2)'
                        }),
                        stroke: new ol.style.Stroke({
                            width: 3,
                            color: 'rgba(30, 144, 255, 1)',
                            lineDash: [0.1, 5]
                        }),
                        zIndex: 2
                    });

                    terrenoDefaultStyle = new ol.style.Style({
                        fill: new ol.style.Fill({
                            color: 'rgba(107, 181, 255, 0.15)'
                        }),
                        stroke: new ol.style.Stroke({
                            color: 'rgb(0, 125, 250)',
                            width: 1
                        }),
                        zIndex: 4
                    });

                    centroSegmentoStyle = new ol.style.Style({
                        image: new ol.style.Circle({
                            radius: 3,
                            fill: new ol.style.Fill({
                                color: '#0000ff'
                            })
                        }),
                        zIndex: 5
                    });

                    function isFeatureEdificacao(feature) {
                        var patt = new RegExp("^E[0-9]+$");
                        return feature.getId() && (feature.getId().includes(featureIds.ACESSAO_BENFEITORIA) || feature.getId().includes(featureIds.UNIDADE_AUTONOMA) || patt.test(feature.getId()));
                    }

                    if (scope.tabIndex === -1) {
                        NotificadorService.subscribe(scope, function (event, scope, geometriaJson) {
                            var geometria;
                            if (geometriaJson) {
                                geometria = geoJson.readGeometry(geometriaJson);
                                viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());
                            }
                        }, notifiers.CENTRALIZAR_POR_MUNICIPIO);

                        NotificadorService.subscribe(scope, function (event, escopo, dado) {
                            removerFeatureById(featureIds.PONTO_IMOVEL);
                            removerFeatureById(featureIds.TERRENO_CADASTRAL);
                            removerFeatureById(featureIds.TERRENO_CARTORIAL);
                            angular.forEach(scope.edificacoes, function (unidadeAutonoma) {
                                removerFeatureById(unidadeAutonoma.edificacao.uuid);
                            });

                            NotificadorService.notify(scope, false, notifiers.FEATURE_EXCLUIDA_UNIDADE_AUTONOMA);
                            redesenharFeaturesRemovidasUnidadeAutonoma();
                            NotificadorService.notify(scope, false, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                            NotificadorService.notify(scope, false, notifiers.PONTO_IN_UNIDADE_AUTONOMA);
                        }, notifiers.REMOVER_PONTO_EDIFICACOES_TERRENO_UA);

                        NotificadorService.subscribe(scope, function (event, escopo, geometriaJson) {
                            var geometria = geoJson.readGeometry(geometriaJson);
                            var feature = new ol.Feature({
                                geometry: geometria
                            });
                            vm.imovel = {};
                            feature.setId(featureIds.PONTO_AMOSTRA);
                            if (imovelSource.getFeatureById(featureIds.PONTO_AMOSTRA)) {
                                imovelSource.removeFeature(imovelSource.getFeatureById(featureIds.PONTO_AMOSTRA));
                            }
                            feature.setStyle(styles.pinAmarelo());
                            imovelSource.addFeature(feature);
                            viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());
                        }, notifiers.MARCAR_PONTO_POR_COORDENADAS);
                    }

                    removerFeaturesUnidadeAutonoma = function (uuidPonto, uuidTerreno, unidadesAutonomas) {
                        angular.forEach(unidadesAutonomas, function (unidadeAutonoma) {
                            angular.forEach(vm.map.getLayers().getArray(), function (layer) {
                                /* TODO: alterar nome da layer unidade autônoma que está sendo passada como parâmetro
                                 após o exército criar um serviço para unidade autonôma. Hoje está benfeitoria.
                                 *  */
                                removeFeatureUnidadeAutonomaByNameLayer(layer, "benfeitoria", unidadeAutonoma.edificacao.uuid);
                            });
                        });

                        angular.forEach(vm.map.getLayers().getArray(), function (layer) {
                            removeFeatureUnidadeAutonomaByNameLayer(layer, featureIds.TERRENO_CARTORIAL, uuidTerreno);
                            removeFeatureUnidadeAutonomaByNameLayer(layer, featureIds.TERRENO_CADASTRAL, uuidTerreno);
                            removeFeatureUnidadeAutonomaByNameLayer(layer, camadas.PONTO_UNIDADE_AUTONOMA, uuidPonto);
                        });
                    };

                    removerFeaturesTerrenoSobrepostoCadastral = function () {
                        angular.forEach(imovelSource.getFeatures(), function (value) {
                            if (value.get('name') === featureIds.TERRENO_CADASTRAL_SOBREPOSTO) {
                                imovelSource.removeFeature(value);
                            }
                        });
                    };

                    removerFeaturesCentroSegmento = function () {
                        angular.forEach(imovelSource.getFeatures(), function (value) {
                            if (value.get('name') === featureIds.CENTRO_SEGMENTO) {
                                imovelSource.removeFeature(value);
                            }
                        });
                    };

                    adicionarCentroSegmentos = function () {
                        var featureCadastral = imovelSource.getFeatureById(featureIds.TERRENO_CADASTRAL),
                            featureCartorial = imovelSource.getFeatureById(featureIds.TERRENO_CARTORIAL),
                            polygon,
                            line,
                            ponto,
                            centroFeature,
                            i;

                        removerFeaturesCentroSegmento();

                        if (featureCartorial) {
                            polygon = featureCartorial.getGeometry();
                        } else if (featureCadastral) {
                            polygon = featureCadastral.getGeometry();
                        }

                        if (polygon) {
                            for (i = 1; i < polygon.getCoordinates()[0].length; i += 1) {
                                line = new ol.geom.LineString([polygon.getCoordinates()[0][i - 1], polygon.getCoordinates()[0][i]]);
                                ponto = new ol.geom.Point(ol.extent.getCenter(line.getExtent()));

                                centroFeature = new ol.Feature({
                                    geometry: ponto,
                                    name: featureIds.CENTRO_SEGMENTO
                                });
                                centroFeature.setStyle(centroSegmentoStyle);
                                imovelSource.addFeature(centroFeature);
                            }
                        }
                    };

                    removerFeature = function (feature) {
                        NotificadorService.notify(scope, feature.getId(), notifiers.FEATURE_EXCLUIDA);
                        imovelSource.removeFeature(feature);
                    };

                    function inserirPonto(feature) {
                        
                        feature.setId(featureIds.PONTO_IMOVEL);
                        var featureJson, feature_, uuidTerreno, confirm, pixel;
                        pixel = vm.map.getPixelFromCoordinate(feature.getGeometry().getCoordinates());

                        var pontoValido = true;
                        featureJson = geoJson.writeFeatureObject(feature).geometry;                       

                        console.log('featureJson');
                        console.log(featureJson);
                                                
                        if (feature_) {
                            uuidTerreno = feature_.get('co_uuid');

                            if (uuidTerreno) {
                                // ImovelTerrenoService.getImoveisByUuidTerrenoService({uuidTerreno: uuidTerreno}).$promise.then(function (imovel) {
                                //
                                //     if (imovel.tipoImovel && imovel.tipoImovel.tipoPai === "UNIDADE AUTÔNOMA") {
                                //         confirm = $mdDialog.confirm({
                                //             focusOnOpen : false,
                                //             textContent : "Caso esteja tentando cadastrar uma unidade autônoma em terreno já cadastrado, o ponto será criado automaticamente e não será necessário cadastrar a geometria e os dados do terreno novamente. Deseja confirmar?",
                                //             ok : "Sim",
                                //             cancel : "Não"
                                //         });
                                //
                                //         $mdDialog.show(confirm).then(function () {
                                //             feature.setId(featureIds.PONTO_IMOVEL);
                                //             scope.edificacoes = imovel.unidadesAutonomas;
                                //             reaproveitarDadosImovelAndEdificacoes (imovel.toJSON(), uuidTerreno);
                                //             NotificadorService.notify(scope, true, notifiers.PONTO_IN_UNIDADE_AUTONOMA);
                                //         }, function () {
                                //             removerFeatureById(featureIds.PONTO_IMOVEL);
                                //         });
                                //
                                //     } else {
                                //         feature_ = imovelSource.getClosestFeatureToCoordinate([feature.getGeometry().getExtent()[0], feature.getGeometry().getExtent()[1]], function (feature) {
                                //             return feature.get('name') === featureIds.CENTRO_SEGMENTO;
                                //         });
                                //
                                //         if (feature_) {
                                //             feature.setGeometry(feature_.getGeometry());
                                //         }
                                //
                                //         feature.setStyle(pontoImovelValidoStyle);
                                //
                                //         validarPonto(feature);
                                //     }
                                // });
                            }

                        } else {
                           
                            feature_ = imovelSource.getClosestFeatureToCoordinate([feature.getGeometry().getExtent()[0], feature.getGeometry().getExtent()[1]], function(feature) {
                                return feature.get('name') === featureIds.CENTRO_SEGMENTO;
                            });

                            if (feature_) {
                                feature.setGeometry(feature_.getGeometry());
                            }  
                            
                            scope.latitude = feature.getGeometry().getExtent()[0];
                            scope.longitude = feature.getGeometry().getExtent()[1];

                            //coordenadas.push([avaliacaoTerreno.imovel.pontoImovel.coordinates[0], avaliacaoTerreno.imovel.pontoImovel.coordinates[1]]);

                            feature.setStyle(pontoImovelValidoStyle);
                            NotificadorService.notify(scope, { feature: feature, isValido: pontoValido }, notifiers.ADICIONAR_FEATURE_PONTO);
                            //validarPonto(feature);
                          
                        }
                    }

                    vm.desbloquearMapa = function (callback) {
                        vm.bloquearMapa = false;
                        if (callback == 'marcar') {
                            vm.marcarPonto();
                        } else {
                            vm.selecionarAmostra();
                        }
                    };

                    setClickEvent = function() {

                        console.log('entrou setClickEvent');
                        vm.map.unByKey(mapClickEvent);
                        mapClickEvent = vm.map.on('click', function(e) {
                            var feature = vm.map.forEachFeatureAtPixel(e.pixel, function(feature) {
                                return feature;
                            });

                            console.log('feature');
                            console.log(feature);

                            console.log('e.coordinate');
                            console.log(e.coordinate);

                            if ((!feature || feature.getId() !== featureIds.PONTO_IMOVEL_TESTADA)) {
                                if (vmr.geometriaMun != undefined) {
                                    new ol.geom.Point(e.coordinate);

                                    var geoJson = {};

                                    if (ciMapaValidacaoFactory.validaCepPontoMunicipio(vmr.geometriaMun, e.coordinate)) {
                                        console.log('ci-mapa-directive - 856');
                                        adicionarMarcador(e.coordinate);
                                        NotificadorService.notify(scope, true, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                                    } else {
                                        console.log('ci-mapa-directive - 859');
                                        MensagensService.exibirMensagemErro("zz O ponto do imóvel não se encontra sobre o município informado em relação ao CEP.");
                                    }
                                } else {
                                    adicionarMarcador(e.coordinate);
                                    NotificadorService.notify(scope, true, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                                }
                            }
                        });
                    };

                    vm.gerarImagem = function (base64) {
                        return 'data:image/png;base64,' + base64;
                    };

                    vm.desbloquearTelaTerreno = function () {
                        vm.bloquearMapaTerreno = false;
                        vm.desenharTerreno();
                    };

                    vm.desbloquearTelaUnidadeAutonoma = function () {
                        vm.bloquearMapaUnidadeAutonoma = false;
                        vm.desenharUnidadeAutonoma();
                    };

                    vm.desbloquearTelaBenfeitoria = function () {
                        vm.bloquearMapaBenfeitoria = false;
                        vm.desenharBenfeitoria();
                    };

                    vm.marcarPonto = function () {
                        
                        if (viewFac.getView().getZoom() >= 13) {

                            removerCustomInteractions();
                            setDrawPoint();

                            vm.map.addInteraction(drawPoint);

                            drawPoint.on('drawend', function(e) {
                                if (vmr.geometriaMun != undefined) {
                                    if (ciMapaValidacaoFactory.validaCepPontoMunicipio(vmr.geometriaMun, e.feature.getGeometry().getCoordinates())) {
                                        inserirPonto(e.feature);
                                        NotificadorService.notify(scope, e.feature.getGeometry().getCoordinates(), notifiers.ATUALIZAR_COORDENADAS_DO_PONTO);
                                    } else {
                                        MensagensService.exibirMensagemErro("O ponto do imóvel não se encontra sobre o município informado em relação ao CEP.");
                                    }
                                } else {
                                    inserirPonto(e.feature);
                                    NotificadorService.notify(scope, e.feature.getGeometry().getCoordinates(), notifiers.ATUALIZAR_COORDENADAS_DO_PONTO);
                                }

                            });

                        } else {
                            MensagensService.exibirMensagemErro("Para incluir um ponto é necessário a aproximação igual ou inferior a 2000 metros.");
                        }
                        
                    };

                    removerCustomInteractions = function () {
                        vm.map.removeInteraction(drawPoint);
                        vm.map.removeInteraction(drawPolygon);
                        vm.map.removeInteraction(drawPolygonBenfeitoria);
                        vm.map.removeInteraction(drawPolygonUnidadeAutonoma);
                        vm.map.removeInteraction(erase);
                    };

                    function validarPonto(feature) {
                        
                        console.log('feature');
                        console.log(feature);

                        console.log('amostra');
                        console.log(amostra);
                        
                        var featureJson;
                        //var uuidTerreno = amostra.terreno.uuid;

                        featureJson = geoJson.writeFeatureObject(feature).geometry;
                        vmr.geomPonto = feature;

                        PontoImovel.save({ 'featureJson': featureJson, 'uuidTerreno': uuidTerreno }).then(function(result) {
                            var confirm,
                                pontoValido = true;

                            // FIXME impedindo validação de sobreposição de ponto do imóvel devido a migração de imóveis do sistema SPIUnet
                            // remova essa linha depois que todos os imóveis migrados forem corrigidos.
                            result.sobrepostoTerreno = false;

                            if ($rootScope.usuarioLogado.jurisdicoes.includes(result.sgUf)) {
                                if (result.sobrepostoTerreno) {
                                    MensagensService.exibirMensagemErro("Não é possível criar outro ponto sobre este imóvel.");
                                    pontoValido = false;
                                } else {
                                    if (result.noMunicipio !== scope.amostra.enderecamento.municipio && scope.amostra.enderecamento.municipio) {
                                        confirm = $mdDialog.confirm({
                                            focusOnOpen: false,
                                            textContent: "O ponto do imóvel deve estar no mesmo município informado nos dados de localização. Deseja continuar?",
                                            ok: "Sim",
                                            cancel: "Não"
                                        });

                                        $mdDialog.show(confirm).then(function() {

                                        }, function() {
                                            pontoValido = false;
                                            scope.amostra.pontoImovel = undefined;
                                            removerFeatureById(featureIds.PONTO_IMOVEL);
                                            feature.setId(featureIds.PONTO_IMOVEL);
                                            feature.setStyle(pontoImovelInvalidoStyle);
                                            vm.removerFeaturesCadastradas();
                                        });
                                    }
                                }
                            } else {
                                MensagensService.exibirMensagemErro("Você não possui permissão para inserir imóveis nesta UF.");
                                pontoValido = false;
                            }
                            if (pontoValido) {
                                scope.amostra.pontoImovel = featureJson;
                                
                                feature.setId(featureIds.PONTO_IMOVEL);
                                feature.setStyle(pontoImovelValidoStyle);
                                //scope.amostra.naturezaImovel.descricao = result.noNaturezaImovel;
                                //validarNaturezaImovel(scope.imovel.naturezaImovel);
                            } else {
                                scope.amostra.pontoImovel = undefined;
                                removerFeatureById(featureIds.PONTO_IMOVEL);
                                feature.setId(featureIds.PONTO_IMOVEL);
                                feature.setStyle(pontoImovelInvalidoStyle);
                            }
                            NotificadorService.notify(scope, { feature: feature, isValido: pontoValido }, notifiers.ADICIONAR_FEATURE_PONTO);
                        }).catch(function() {
                            MensagensService.exibirMensagemErro("GeoServiço indisponível! Por favor, tente novamente mais tarde.");
                        });
                    }

                    setDrawPoint = function () {
                        drawPoint = new ol.interaction.Draw({
                            source: imovelSource,
                            type: 'Point',
                            style: styles.pinPreto()
                        });
                    };

                    vm.removerPonto = function () {
                        if (amostra) {
                            if (imovelSource.getFeatureById(amostra.id)) {
                                imovelSource.removeFeature(imovelSource.getFeatureById(amostra.id));
                                amostra = null;
                                vm.imovel = null;
                                vm.latitude = null;
                                vm.longitude = null
                            }
                        }
                        if (imovelSource.getFeatureById(featureIds.PONTO_AMOSTRA)) {
                            imovelSource.removeFeature(imovelSource.getFeatureById(featureIds.PONTO_AMOSTRA));
                        }
                    };

                    // vm.marcarPonto = function () {
                    //     console.log('entrou marcarPonto 934');
                    //     removerCustomInteractions();
                    //     setDrawPoint();
                    //     vm.adicionandoPonto = true;
                    //     vm.map.addInteraction(drawPoint);

                    //     drawPoint.on('drawend', function (e) {
                    //         var feature = e.feature;
                    //         vm.imovel = {};
                    //         feature.setId(featureIds.PONTO_AMOSTRA);
                    //         if (amostra) {
                    //             if (imovelSource.getFeatureById(amostra.id)) {
                    //                 imovelSource.removeFeature(imovelSource.getFeatureById(amostra.id));
                    //             }
                    //         }
                    //         if (imovelSource.getFeatureById(featureIds.PONTO_AMOSTRA)) {
                    //             imovelSource.removeFeature(imovelSource.getFeatureById(featureIds.PONTO_AMOSTRA));
                    //         }
                    //         feature.setStyle(styles.pinPreto());
                    //         $timeout(function () {
                    //             vm.imovel.coordinates = feature.getGeometry().getCoordinates();
                    //             vm.latitude = vm.imovel.coordinates[0];
                    //             vm.longitude = vm.imovel.coordinates[1];
                    //             vm.imovel.type = 'Point';
                    //         })
                    //         vm.map.removeInteraction(drawPoint);

                    //     });

                    //     vm.adicionandoPonto = false;
                    // };

                    vm.selecionarAmostra = function () {
                        vm.map.on('click', function (e) {
                            var features = [];
                            if (vm.map.hasFeatureAtPixel(e.pixel)) {
                                vm.map.forEachFeatureAtPixel(e.pixel, function (feature) {
                                    features.push(feature);
                                });
                                if (features) {
                                    for (var i = 0; i < vm.amostras.length; i++) {
                                        if (vm.amostras[i].id === features[0].getId()) {
                                            vm.amostra = vm.amostras[i];
                                            vm.latitude = vm.amostra.latitude;
                                            vm.longitude = vm.amostra.longitude;
                                            var ponto = {
                                                coordinates: [vm.amostra.latitude, vm.amostra.longitude],
                                                type: 'Point'
                                            };
                                            marcarPontoSelecionado(ponto);
                                            break;
                                        }
                                    }
                                }

                            }
                        });

                        function marcarPontoSelecionado(geometriaJson) {
                            var geometria = geoJson.readGeometry(geometriaJson);
                            var feature = new ol.Feature({
                                geometry: geometria
                            });
                            feature.setId(featureIds.PONTO_AMOSTRA);
                            if (imovelSource.getFeatureById(featureIds.PONTO_AMOSTRA)) {
                                imovelSource.removeFeature(imovelSource.getFeatureById(featureIds.PONTO_AMOSTRA));
                            }
                            feature.setStyle(styles.pinPreto());
                            imovelSource.addFeature(feature);
                        }
                    };

                    vm.desenharTerreno = function () {
                        if (viewFac.getView().getZoom() >= 13) {
                            removerCustomInteractions();
                            setDrawPolygon();
                            vm.map.addInteraction(drawPolygon);

                            drawPolygon.on('drawstart', function () {
                                vm.map.unByKey(mapClickEvent);
                            });

                            drawPolygon.on('drawend', function (e) {
                                validarGeometriaTerreno(e.feature, featureIds.TERRENO_CADASTRAL, "vetorizacao");
                                NotificadorService.notify(scope, false, notifiers.VERIFICAR_PONTO_NA_ARESTA);
                                vm.map.removeInteraction(drawPolygon);
                            });
                        }
                        // else {
                        //     MensagensService.exibirMensagemErro("Para vetorizar a área do terreno é necessário a aproximação igual ou inferior a 2000 metros.");
                        // }
                    };

                    validarGeometriaBenfeitoria = function (featureTerreno, tipoTerreno, tipoDesenho) {
                        var terreno, EDIFICACAO = 1, benfeitoria = {}, geometriaBenfeitoria = geoJson.writeFeatureObject(featureTerreno).geometry, terrenoFeature, benfeitoriasImovel = scope.imovel.benfeitorias ? scope.imovel.benfeitorias : [], benfeitorias = [];
                        benfeitoria.tipo = EDIFICACAO;
                        benfeitoria.geom = {};
                        benfeitoria.geom.geometry = geometriaBenfeitoria;
                        benfeitoria.geom.type = geometriaBenfeitoria.type;

                        if (imovelSource.getFeatureById(featureIds.TERRENO_CARTORIAL)) {
                            terreno = imovelSource.getFeatureById(featureIds.TERRENO_CARTORIAL);
                        } else if (imovelSource.getFeatureById(featureIds.TERRENO_CADASTRAL)) {
                            terreno = imovelSource.getFeatureById(featureIds.TERRENO_CADASTRAL);
                        }
                        terrenoFeature = geoJson.writeFeatureObject(terreno).geometry;

                        if (benfeitoriasImovel.length > 0) {
                            for (var i = 0; i < benfeitoriasImovel.length; i++) {
                                if (benfeitoriasImovel[i].tipoBenfeitoria.id === 1) {
                                    var benf = {};
                                    benf.geom = {};
                                    benf.tipo = EDIFICACAO;

                                    benf.geom.geometry = benfeitoriasImovel[i].geometriaBenfeitoria;
                                    benf.geom.type = benfeitoriasImovel[i].geometriaBenfeitoria.type;
                                    if (benfeitoriasImovel[i].codigo != tipoTerreno) {
                                        benfeitorias.push(benf);
                                    }
                                }
                            }
                        }
                        benfeitorias.push(benfeitoria);
                    };

                    vm.desenharBenfeitoria = function () {
                        if (viewFac.getView().getZoom() >= 13) {
                            removerCustomInteractions();
                            setDrawPolygonBenfeitoria();
                            vm.map.addInteraction(drawPolygonBenfeitoria);

                            drawPolygonBenfeitoria.on('drawend', function (e) {
                                var id = scope.benfeitoria.codigo || featureIds.ACESSAO_BENFEITORIA,
                                    area,
                                    geom = e.feature.getGeometry();

                                validarGeometriaBenfeitoria(e.feature, id, "vetorizacao");

                                area = formatarArea(geom);
                                removerFeatureById(id);
                                e.feature.setStyle(benfeitoriaStyle);
                                e.feature.setId(id);
                                e.feature.set('name', 'benfeitoria');
                                e.feature.set('tipoDesenho', 'vetorizacao');
                                imovelSource.addFeature(e.feature);
                                scope.benfeitoria.geometriaBenfeitoria = geoJson.writeFeatureObject(e.feature).geometry;
                                NotificadorService.notify(scope, e.feature, notifiers.ADICIONAR_FEATURE_BENFEITORIA);
                                vm.map.removeInteraction(drawPolygonBenfeitoria);
                            });
                        }
                        // else {
                        //     MensagensService.exibirMensagemErro("Para vetorizar a área da acessão/benfeitoria é necessário a aproximação igual ou inferior a 2000 metros.");
                        // }
                    };

                    vm.desenharUnidadeAutonoma = function () {
                        if (viewFac.getView().getZoom() >= 13) {
                            removerCustomInteractions();
                            setDrawPolygonUnidadeAutonoma();
                            vm.map.addInteraction(drawPolygonUnidadeAutonoma);

                            drawPolygonUnidadeAutonoma.on('drawend', function (e) {
                                var id = scope.imovel.unidadeAutonoma.edificacao.uuid || featureIds.UNIDADE_AUTONOMA;
                                removerFeatureById(id);
                                e.feature.setId(id);
                                e.feature.setStyle(unidadeAutonomaStyle);
                                e.feature.set('tipoDesenho', 'vetorizacao');
                                imovelSource.addFeature(e.feature);
                                NotificadorService.notify(scope, e.feature, notifiers.ADICIONAR_FEATURE_UNIDADE_AUTONOMA);
                                scope.imovel.unidadeAutonoma.edificacao.geometria = geoJson.writeFeatureObject(e.feature).geometry;
                                vm.map.removeInteraction(drawPolygonUnidadeAutonoma);
                            });
                        }
                        // else {
                        //     MensagensService.exibirMensagemErro("Para vetorizar a área da unidade autônoma é necessário a aproximação igual ou inferior a 2000 metros.");
                        // }
                    };

                    vm.apagar = function () {
                        removerCustomInteractions();
                        setErase();
                        vm.map.addInteraction(erase);

                        erase.on('select', function (e) {
                            if (e.selected.length > 0) {
                                var selected = e.selected[0],
                                    id;

                                if (action === 'add') {
                                    if (scope.tabIndex === -1) {
                                        if (selected.getId() === featureIds.PONTO_IMOVEL) {
                                            removerFeature(selected);
                                        }
                                    } else if (scope.tabIndex === 1) {
                                        if (selected.getId() === featureIds.TERRENO_CADASTRAL) {
                                            removerFeature(selected);
                                            adicionarCentroSegmentos();
                                            removerFeaturesTerrenoSobrepostoCadastral();
                                        }
                                    } else if (scope.tabIndex === 3) {
                                        if (selected.getId() === featureIds.UNIDADE_AUTONOMA) {
                                            removerFeature(selected);
                                        }
                                    } else if (scope.tabIndex === 4) {
                                        id = scope.benfeitoria.codigo || featureIds.ACESSAO_BENFEITORIA;
                                        if (selected.getId() === id) {
                                            removerFeatureById(id);
                                            scope.benfeitoria.geometriaBenfeitoria = undefined;
                                        }
                                    }
                                } else if (action === 'edit') {
                                    if (scope.tabIndex === 1) {
                                        if (selected.getId() === featureIds.TERRENO_CADASTRAL) {
                                            removerFeature(selected);
                                            adicionarCentroSegmentos();
                                            removerFeaturesTerrenoSobrepostoCadastral();
                                        }
                                    } else if (scope.tabIndex === 4) {
                                        id = scope.benfeitoria.codigo || featureIds.ACESSAO_BENFEITORIA;
                                        if (selected.getId() === id) {
                                            removerFeatureById(id);
                                            scope.benfeitoria.geometriaBenfeitoria = undefined;
                                        }
                                    }
                                }
                                vm.map.unByKey(mapClickEvent);
                            }
                            vm.map.removeInteraction(erase);
                        });
                    };

                    vm.alterarBingVisible = function () {
                        angular.forEach(vm.map.getLayers(), function (value) {
                            if (value.get('name') === 'bingMapa') {
                                value.setVisible(!value.getVisible());
                                NotificadorService.notify(scope, value.getVisible(), notifiers.ALTERAR_VISIBILIDADE_BING);
                            }
                        });

                    };

                    vm.alterarLayerVisible = function (name) {
                        angular.forEach(vm.map.getLayers(), function (value) {
                            if (value.get('name') === name) {
                                value.setVisible(!value.getVisible());
                            }
                        });
                    };

                    vm.layerFilter = function (layer) {
                        return !layer.get('required');
                    };

                    vm.direcionarDetalharLocalizacao = function (idImovel) {
                        if (angular.isFunction(scope.$parent.fechar)) {
                            scope.$parent.fechar();
                        }

                        if (idImovel) {
                            if ($state.current.name === 'INCORPORACAO.homologarImovel') {
                                $state.go('INCORPORACAO.homologarImovelDetalhar', {id: idImovel});
                            } else {
                                $state.go('INCORPORACAO.detalharImovel', {id: idImovel});
                            }
                        }
                    };

                    vm.direcionarDetalharPesquisar = function (imovelSelecionado) {
                        if (action === 'pesquisarImovel') {
                            $state.go('INCORPORACAO.detalharImovel', {id: imovelSelecionado.id});
                        } else {
                            scope.$parent.fechar();
                            $state.go('INCORPORACAO.homologarImovelDetalhar', {id: imovelSelecionado.id});
                        }

                    };

                    vm.alterarVisibilidadeBalaoCamadas = function () {
                        vm.listaLayers = !vm.listaLayers;
                    };

                    setStyleTerrenoByFeature = function (feature, tipoTerreno) {
                        if (tipoTerreno === featureIds.TERRENO_CADASTRAL) {
                            var terrenoCartorial = imovelSource.getFeatureById(featureIds.TERRENO_CARTORIAL);
                            if (terrenoCartorial) {
                                feature.setStyle(terrenoValidoPontilhadoStyle);
                            } else {
                                feature.setStyle(terrenoValidoStyle);
                            }
                        } else {
                            feature.setStyle(terrenoValidoStyle);
                        }
                    };

                    formatarArea = function (polygon) {
                        var area, output;
                        area = polygon.getArea();
                        output = (Math.round(area * 100) / 100);
                        return output;
                    };

                    desenhaPorGeometria = function (geometry, tipoTerreno, acao) {
                        var feature, geometria;

                        if (acao === 'vetorizacao') {
                            geometria = geometry;
                        } else {
                            geometria = geoJson.readGeometry(geometry);
                        }

                        removerFeatureById(tipoTerreno);
                        removerFeaturesCentroSegmento();

                        feature = new ol.Feature({
                            geometry: geometria
                        });

                        feature.setId(tipoTerreno);
                        feature.set('tipoDesenho', acao);
                        imovelSource.addFeature(feature);
                        return feature;
                    };

                    destacarBenfeitoria = function (id) {
                        angular.forEach(imovelSource.getFeatures(), function (feature) {
                            if (feature.get('name') === 'benfeitoria') {
                                if (feature.getId() === id) {
                                    feature.setStyle(benfeitoriaDestacadaStyle);
                                } else {
                                    feature.setStyle(benfeitoriaStyle);
                                }
                            }
                        });
                    };

                    getBenfeitoriaByCodigo = function (codigo) {
                        var benfeitorias = scope.imovel.benfeitorias,
                            i;
                        for (i = 0; i < benfeitorias.length; i += 1) {
                            if (benfeitorias[i].codigo === codigo) {
                                return benfeitorias[i];
                            }
                        }
                    };

                    centralizarPorTerreno = function () {
                        if (geometriaImovelTerrenoCartorial) {
                            viewFac.centralizarMapa(geometriaImovelTerrenoCartorial.getExtent(), vm.map.getSize());
                        } else if (geometriaImovelTerrenoCadastral) {
                            viewFac.centralizarMapa(geometriaImovelTerrenoCadastral.getExtent(), vm.map.getSize());
                        }
                    };

                    getElementHTMLOverlay = function (benfeitoria) {
                        return "<div layout='column' class='ng-scope'>" +
                            "<div layout='row'><p>" +
                            "<b>ID: </b>" +
                            benfeitoria.codigo +
                            "</p></div>" +
                            "<div layout='row'><p><b>" +
                            "Tipo: " +
                            "</b>" +
                            benfeitoria.tipoBenfeitoria.descricao + " / " + benfeitoria.edificacao.tipoEspecializacaoEdificacao.descricao +
                            "</p></div>" +
                            "<div layout='row'><p><b>Nome: </b>" + benfeitoria.nome + "</p></div>" +
                            "</div>";
                    };

                    getImovelOverlayHTML = function () {

                        return "<div layout='row' ng-repeat='imovel in imoveisOverlay' ng-click='direcionarDetalharLocalizacao(imovel.id)'>" +
                            "<img class='overlay-image' ng-if='imovel.imagens[0]' src='data:image/jpeg;base64,{{imovel.imagens[0].imagem.arquivo}}'/>" +
                            "<img class='overlay-image' ng-if='!imovel.imagens[0]' src='./assets/img/no-image.png'/>" +
                            "<md-content class='overlay-content' style='margin-left: 10px'>" +
                            "<p><b>" +
                            "RIP: {{imovel.rip}} " +
                            "</b></p>" +
                            "<p><b>" +
                            "Endereço: {{imovel.enderecamento.tipoLogradouro + ' ' + imovel.enderecamento.logradouro + ' - ' + imovel.numero + ' ' + imovel.enderecamento.municipio + ' ' + imovel.enderecamento.bairro}}  " +
                            "</b></p>" +
                            "<p><b>" +
                            "CEP: {{imovel.enderecamento.cep}}" +
                            "</b></p>" +
                            "</md-content>" +
                            "</div>";

                    };

                    getImovelById = function (id) {
                        var i;
                        for (i = 0; i < vm.resultadoPesquisarImovel.length; i += 1) {
                            if (vm.resultadoPesquisarImovel[i].id === id) {
                                return vm.resultadoPesquisarImovel[i];
                            }
                        }
                    };

                    desenharGeometriaEdificacao = function (edificacao) {
                        var geometria = edificacao.geometria, geometriaUnidadeAutonoma, featureUnidadeAutonoma;

                        if (geometria) {
                            geometriaUnidadeAutonoma = geoJson.readGeometry(geometria);
                            removerFeatureById(featureIds.UNIDADE_AUTONOMA);
                            featureUnidadeAutonoma = new ol.Feature({});
                            featureUnidadeAutonoma.setId(edificacao.uuid);
                            featureUnidadeAutonoma.setStyle(unidadeAutonomaStyle);
                            featureUnidadeAutonoma.set('tipoDesenho', 'vetorizacao');
                            featureUnidadeAutonoma.setGeometry(geometriaUnidadeAutonoma);
                            featureUnidadeAutonoma.set('co_uuid', edificacao.uuid);
                            imovelSource.addFeature(featureUnidadeAutonoma);
                            if (scope.tabIndex !== 3) {
                                NotificadorService.notify(scope, featureUnidadeAutonoma, notifiers.ADICIONAR_FEATURE_UNIDADE_AUTONOMA);
                            }
                        }
                    };

                    reaproveitarDadosImovelAndEdificacoes = function (imovel, uuidTerreno) {

                        var uuidPonto, featureCadastral, featureCartorial, geometriaPonto;
                        if (scope.imovel.tipoImovel && scope.imovel.tipoImovel.tipoPai !== tiposImovel.UNIDADE_AUTONOMA) {
                            scope.imovel.tipoImovel = undefined;
                            NotificadorService.notify(scope, undefined, notifiers.LIMPAR_TIPO_IMOVEL);
                        } else {
                            scope.imovel.tipoImovel = imovel.tipoImovel;
                        }
                        scope.imovel.enderecamento = imovel.enderecamento;
                        scope.imovel.pontoImovel = imovel.pontoImovel;
                        scope.imovel.terreno = Object.assign(scope.imovel.terreno, imovel.terreno);
                        scope.imovel.terreno.unidadeMedidaCadastral = 'm2';
                        scope.imovel.terreno.unidadeMedidaCartorial = 'm2';
                        scope.imovel.naturezaImovel = imovel.naturezaImovel;
                        uuidPonto = scope.imovel.uuid;

                        removerFeaturesUnidadeAutonoma(uuidPonto, uuidTerreno, imovel.unidadesAutonomas);

                        if (scope.imovel.terreno.geometriaTerrenoCartorial) {
                            featureCartorial = desenhaPorGeometria(scope.imovel.terreno.geometriaTerrenoCartorial, featureIds.TERRENO_CARTORIAL, null);
                            featureCartorial.set('co_uuid', uuidTerreno);
                            setStyleTerrenoByFeature(featureCartorial, featureIds.TERRENO_CARTORIAL);
                            NotificadorService.notify(scope, {feature: featureCartorial, isValid: true, intersecoes: {}}, notifiers.ADICIONAR_FEATURE_TERRENO);
                        }

                        if (scope.imovel.terreno.geometriaTerreno) {
                            featureCadastral = desenhaPorGeometria(scope.imovel.terreno.geometriaTerreno, featureIds.TERRENO_CADASTRAL, null);
                            featureCadastral.set('co_uuid', uuidTerreno);
                            setStyleTerrenoByFeature(featureCadastral, featureIds.TERRENO_CADASTRAL);
                            NotificadorService.notify(scope, {feature: featureCadastral, isValid: true, intersecoes: {}}, notifiers.ADICIONAR_FEATURE_TERRENO);
                        }

                        angular.forEach(imovel.unidadesAutonomas, function (unidadeAutonoma) {
                            desenharGeometriaEdificacao(unidadeAutonoma.edificacao);
                        });

                        if (scope.imovel.pontoImovel) {
                            geometriaPonto = geoJson.readGeometry(scope.imovel.pontoImovel);
                            feature.setGeometry(geometriaPonto);
                            removeAndSetPonto(feature);
                            NotificadorService.notify(scope, {feature: feature, isValido: true}, notifiers.ADICIONAR_FEATURE_PONTO);
                        }
                    };

                    redesenharFeaturesRemovidasUnidadeAutonoma = function () {
                        angular.forEach(featuresRemovidasUnidadeAutonomas, function (feature) {
                            if (feature.getGeometry() && feature.getGeometry().getType() != 'Point') {
                                feature.setStyle(styles.terrenoCinza());
                                if (feature.getId().includes('benfeitoria')) {
                                    adicionaFeatureByLayer('benfeitoria', feature);
                                }

                                if (feature.getId().includes('cadastral')) {
                                    adicionaFeatureByLayer(featureIds.TERRENO_CADASTRAL, feature);
                                }

                                if (feature.getId().includes('cartorial')) {
                                    adicionaFeatureByLayer(featureIds.TERRENO_CARTORIAL, feature);
                                }

                            } else if (feature.getGeometry() && feature.getGeometry().getType() == 'Point') {
                                adicionaFeatureByLayer(camadas.PONTO_UNIDADE_AUTONOMA, feature);
                            }
                        });
                    };

                    adicionaFeatureByLayer = function (layerName, feature) {

                        if (layerName == featureIds.PONTO_UNIDADE_AUTONOMA) {
                            angular.forEach(vm.map.getLayers().getArray(), function (layer) {
                                if (layer.get('name') === layerName) {
                                    layer.getSource().getSource().addFeature(feature);
                                }
                            });
                        } else {
                            angular.forEach(vm.map.getLayers().getArray(), function (layer) {
                                if (layer.get('name') === layerName) {
                                    layer.getSource().addFeature(feature);
                                }
                            });
                        }

                    };

                    vm.removerAmostra = function (amostra) {
                        if (amostra.adicionado) {
                            for (var i = 0; i < vm.listaAmostras.length; i++) {
                                if (amostra.id === vm.listaAmostras[i].id) {
                                    vm.listaAmostras.splice(i, 1);
                                    amostra.adicionado = false;
                                    var feature = imovelSource.getFeatureById(amostra.id);
                                    feature.setStyle(styles.pinPreto())
                                }
                            }
                        }
                    };

                    vm.adicionarAmostra = function (amostra) {
                        for (var i = 0; i < vm.listaAmostras.length; i++) {
                            if (amostra.id === vm.listaAmostras[i].id) {
                                return
                            }
                        }
                        var feature = imovelSource.getFeatureById(amostra.id);
                        feature.setStyle(styles.pinPreto())
                        amostra.adicionado = true;
                        vm.listaAmostras.push(amostra);
                    };

                    vm.cancelar_Amostra = function (amostra) {
                        vm.cancelarAmostra({idAmostra: amostra.id});
                    };
                    vm.editar_Amostra = function (amostra) {
                        vm.editarAmostra({idAmostra: amostra.id});
                    };
                    vm.detalhar_Amostra = function (amostra) {
                        vm.visualizarAmostra({idAmostra: amostra.id});
                    };

                    if (vm.mapaVariaveisAmostras === true) {
                        NotificadorService.subscribe(scope, function (event, escopo, avaliacaoTerreno) {

                            if (avaliacaoTerreno.amostras && avaliacaoTerreno.amostras.length > 0) {
                                vm.amostras = angular.copy(avaliacaoTerreno.amostras);
                                var coordenadas = [];
                                for (var i = 0; i < avaliacaoTerreno.amostras.length; i++) {
                                    var geo = {
                                        coordinates: [avaliacaoTerreno.amostras[i].latitude, avaliacaoTerreno.amostras[i].longitude],
                                        type: 'Point'
                                    };
                                    var geometria = geoJson.readGeometry(geo);
                                    if (geometria) {
                                        var feature = new ol.Feature({
                                            geometry: geometria
                                        });
                                        feature.setId(avaliacaoTerreno.amostras[i].id);
                                        if (avaliacaoTerreno.amostras[i].pinAmarelo) {
                                            amostra = avaliacaoTerreno.amostras[i];
                                            feature.setStyle(styles.pinPreto());
                                        } else {
                                            feature.setStyle(styles.pinPreto());
                                        }

                                        imovelSource.addFeature(feature);
                                    }
                                    coordenadas.push([avaliacaoTerreno.amostras[i].latitude, avaliacaoTerreno.amostras[i].longitude])
                                }
                            }
                            coordenadas.push([avaliacaoTerreno.imovel.pontoImovel.coordinates[0], avaliacaoTerreno.imovel.pontoImovel.coordinates[1]]);
                            var view = vm.map.getView();
                            var coord = new ol.geom.MultiPoint(coordenadas);
                            view.fit(coord, vm.map.getSize());

                        }, notifiers.MARCAR_PONTOS_AVALIACAO_TERRENO)
                    }

                    init();
                });

            }
        };
    }]);
}());

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cadastrarValorEdificacaoController',
            ["$scope", "avaliacaoEscopoCompartilhadoService", "arquivoAvaliacaoService", "formularioAvaliacaoService", "numeroExtensoAvaliacaoService", "$state", "ComumFactory", "$filter", function ($scope, avaliacaoEscopoCompartilhadoService, arquivoAvaliacaoService, formularioAvaliacaoService, numeroExtensoAvaliacaoService, $state, ComumFactory, $filter) {

                var vm = $scope;

                vm.anexos = [];

                vm.TIPO_BENFEITORIA = "TIPO_BENFEITORIA";
                vm.CONTENT_TYPE_02 = 'CONTENT_TYPE_02';

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                var init = function () {

                    recuperarPropriedadesLaudoRelatorio();

                    if (vm.tipoTerreno === vm.TIPO_BENFEITORIA) {
                        vm.codigoBenfeitoria = vm.benfeitoria.codigo;
                        vm.codigoBenfeitoria += ' - ';
                        vm.codigoBenfeitoria += vm.benfeitoria.edificacao.tipoProjeto.descricao;
                        vm.codigoBenfeitoria += '/';
                        vm.codigoBenfeitoria += vm.benfeitoria.edificacao.tipoEspecializacaoEdificacao.descricao;
                    }
                };

                var recuperarPropriedadesLaudoRelatorio = function () {
                    vm.laudoRelatorioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoRelatorioAvaliacao');
                    vm.dadosEnderecamento = avaliacaoEscopoCompartilhadoService.getObjeto('dadosEnderecamento');
                    vm.dadosProprietario = avaliacaoEscopoCompartilhadoService.getObjeto('dadosProprietario');
                    vm.benfeitoria = avaliacaoEscopoCompartilhadoService.getObjeto('benfeitoria');
                    vm.tipoTerreno = avaliacaoEscopoCompartilhadoService.getObjeto('tipoTerreno');

                    /**Identificacao**/
                    vm.ripImovel = avaliacaoEscopoCompartilhadoService.getObjeto('ripImovel');
                    vm.pontoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('pontoImovel');
                    vm.listaResponsaveisTecnicos = avaliacaoEscopoCompartilhadoService.getObjeto('listaResponsaveisTecnicos');
                    vm.listaNormasUtilizadas = avaliacaoEscopoCompartilhadoService.getObjeto('listaNormasUtilizadas');
                    /**Detalher Imovel**/
                    vm.listaMatriculasTranscricao = avaliacaoEscopoCompartilhadoService.getObjeto('listaMatriculasTranscricao');
                    vm.listaCertidoesCartoriais = avaliacaoEscopoCompartilhadoService.getObjeto('listaCertidoesCartoriais');
                    angular.forEach(vm.listaCertidoesCartoriais, function (certidaoCartorial) {
                        certidaoCartorial.arquivo.id = null;
                    });
                    vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                    vm.listaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosImovel');
                    /**Terreno Imovel**/
                    vm.terreno = avaliacaoEscopoCompartilhadoService.getObjeto('terreno');
                    vm.documentosParametrosUrbanisticos = avaliacaoEscopoCompartilhadoService.getObjeto('documentosParametrosUrbanisticos');
                    /**Benfeitorias**/
                    vm.listaBenfeitoriaAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriaAvaliacao');
                    vm.listaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosBenfeitorias');
                    /**Unidade Autonoma**/
                    vm.unidadeAutonomaIncorporacao = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaIncorporacao');
                    vm.listaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosUnidadeAutonoma');
                    /**Vistoria**/
                    vm.vistoriaListaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosImovel');
                    vm.vistoriaListaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosBenfeitorias');
                    vm.vistoriaListaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosUnidadeAutonoma');
                    /**Avaliacao**/
                    vm.tabSelect = avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect');
                    vm.avaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao');
                    /**Anexo**/
                    vm.anexoLaudoRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('anexoLaudoRelatorio');

                    vm.formularioIdentificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioIdentificacao');
                    vm.formularioDetalhesImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioDetalhesImovel');
                    vm.formularioTerrenoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioTerrenoImovel');
                    vm.formularioBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('formularioBenfeitorias');
                    vm.formularioUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUnidadeAutonoma');
                    vm.formularioUtilizacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUtilizacao');
                    vm.formularioVistoria = avaliacaoEscopoCompartilhadoService.getObjeto('formularioVistoria');
                    vm.formularioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAvaliacao');
                    vm.formularioResultadosRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosRelatorio');
                    vm.formularioResultadosLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosLaudo');
                    vm.formularioQuantificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacao');
                    vm.formularioQuantificacaoOrcamento = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacaoOrcamento');
                    vm.formularioResultadoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadoAvaliacao');
                    vm.formularioAnexo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAnexo');

                    avaliacaoEscopoCompartilhadoService.limparEscopo();
                };

                var voltarCadastrarLaudoRelatorio = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', vm.tipoTerreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', vm.benfeitoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);
                    /**Anexos**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('anexoLaudoRelatorio', vm.anexoLaudoRelatorio);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

                    $state.go('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: vm.laudoRelatorioAvaliacao.id
                    });
                };

                vm.fecharInserirValorEdificacao = function () {
                    voltarCadastrarLaudoRelatorio();
                };

                vm.salvar = function (formularioDados) {
                    vm.validarFormulario(formularioDados);

                    if (!formularioDados.$valid) {
                        return;
                    }

                    if (vm.tipoTerreno === vm.TIPO_BENFEITORIA) {
                        vm.benfeitoria.valorEdificacao = {};
                        vm.benfeitoria.valorEdificacao.valor = vm.valor;
                        vm.benfeitoria.valorEdificacao.descricaoEstimativa = vm.descricaoEstimativa;
                        vm.benfeitoria.valorEdificacao.anexos = vm.anexos;

                        angular.forEach(vm.listaBenfeitoriasImovel, function (benfeitoriaImovel) {
                            if (benfeitoriaImovel.id === vm.benfeitoria.id) {
                                benfeitoriaImovel = angular.copy(vm.benfeitoria);
                            }
                        });

                    }else {
                        vm.unidadeAutonomaIncorporacao.valorEdificacao = {};
                        vm.unidadeAutonomaIncorporacao.valorEdificacao.valor = vm.valor;
                        vm.unidadeAutonomaIncorporacao.valorEdificacao.descricaoEstimativa = vm.descricaoEstimativa;
                        vm.unidadeAutonomaIncorporacao.valorEdificacao.anexos = vm.anexos;
                    }
                    ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-salvos'));
                    voltarCadastrarLaudoRelatorio();
                };

                vm.convertNumeroPorExtenso = function (numero) {
                    vm.valorPorExtenso = numeroExtensoAvaliacaoService.converterNumeroPorExtenso(numero, "real");
                };

                init();
            }])

})();

(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory("consultaAcoesAvaliacaoService", ["$http", function($http){
            var URL_BASE = '/avaliacao/api/acao-avaliacao';

            var consultarAcoesAvaliacao = function(filtroConsulta, offset, limit) {
                return $http.post(URL_BASE + '/filtro-acoes?page=' + offset + '&size=' + limit, filtroConsulta);
            };


            var salvarAcaoAvaliacao = function(acaoAvaliacao) {
                return $http.post(URL_BASE, acaoAvaliacao);
            };

            var salvarAcaoPvg = function(acaoAvaliacao) {
                return $http.post(URL_BASE + '/salvar-pvg', acaoAvaliacao);
            };


            var salvarAcaoPvgSpu = function(acaoAvaliacao) {
                return $http.post(URL_BASE + '/salvar-pvg-spu', acaoAvaliacao);
            };

            var consultarAcoesAvaliacaoLaudo = function(idLaudoAvaliacao) {
                return $http.get(URL_BASE + '/buscar-acoes-laudo/' + idLaudoAvaliacao);
            };

            var consultarAcoesAvaliacaoPvg = function(idPvg) {
                return $http.get(URL_BASE + '/buscar-acoes-pvg/' + idPvg);
            };

            var consultarAcoesAvaliacaoPvgSpu = function(idPvg) {
                return $http.get(URL_BASE + '/buscar-acoes-pvg-spu/' + idPvg);
            };

            var consultarAcaoId = function(idAcaoAvaliacao) {
                return $http.get(URL_BASE + '/buscar-acao/' + idAcaoAvaliacao);
            };

            var consultarTiposAcao = function() {
                return $http.get(URL_BASE + '/buscar-tipos-acao');
            };

            var consultarTiposCargo = function() {
                return $http.get(URL_BASE + '/buscar-tipos-cargo');
            };

            var consultarTipoAcaoExecutada = function() {
                return $http.get(URL_BASE + '/buscar-tipos-acao-executada');
            };

            var consultarStatusAcao = function() {
                return $http.get(URL_BASE + '/buscar-status-acao');
            };

            return {
                consultarAcoesAvaliacao: consultarAcoesAvaliacao,
                salvarAcaoPvg: salvarAcaoPvg,
                salvarAcaoAvaliacao: salvarAcaoAvaliacao,
                consultarAcoesAvaliacaoLaudo: consultarAcoesAvaliacaoLaudo,
                consultarAcoesAvaliacaoPvg: consultarAcoesAvaliacaoPvg,
                consultarAcaoId: consultarAcaoId,
                consultarStatusAcao: consultarStatusAcao,
                consultarTiposCargo: consultarTiposCargo,
                consultarTiposAcao: consultarTiposAcao,
                consultarTipoAcaoExecutada: consultarTipoAcaoExecutada,
                salvarAcaoPvgSpu: salvarAcaoPvgSpu,
                consultarAcoesAvaliacaoPvgSpu: consultarAcoesAvaliacaoPvgSpu
            }
        }])
})();
(function () {
    'use strict';

    angular.module('su-avaliacao').controller('HistoricoAcoesAvaliacaoController',
        ["$scope", "$filter", "consultaAcoesAvaliacaoService", "$state", "$mdMedia", "$mdDialog", "$stateParams", "ComumFactory", "usuarioAvaliacaoService", function ($scope, $filter, consultaAcoesAvaliacaoService,
                  $state, $mdMedia, $mdDialog, $stateParams, ComumFactory, usuarioAvaliacaoService) {

            var vm = $scope;

            vm.topDirections = ['left'];
            vm.bottomDirections = ['down', 'right'];
            vm.isOpen = false;
            vm.availableModes = ['md-fling', 'md-scale'];
            vm.selectedMode = 'md-scale';
            vm.availableDirections = ['right'];
            vm.selectedDirection = 'right';

            vm.idAcaoAvaliacao = $stateParams.idAcaoAvaliacao;
            vm.acaoAvaliacao = {};
            vm.laudoAvaliacao = {};
            vm.listaTiposAcao = [];
            vm.listaStatusAcao = [];

            vm.listaAcoes = {};
            vm.registroInicial = 1;
            vm.tamanhoLimite = 5;
            vm.totalItems = 0;

            var buscarUsuarioLogado = function (callback) {
                usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                    vm.usuarioLogado = resp.data;
                    if (vm.usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == 'false' && vm.usuarioLogado.AVALIACAO_ACOES_CHEFIA == 'false' && vm.usuarioLogado.AVALIACAO_ACOES_TECNICO == 'false') {
                        $state.go('avaliacao.ACESSO_NEGADO');
                    }
                    if (callback) {
                        callback();
                    }
                });
            };

            var init = function () {
                buscarUsuarioLogado(function () {
                    pesquisaTiposAcao();
                    pesquisaStatusAcao();
                    pesquisaTipoAcaoExecutada();
                    pesquisaTiposCargo();
                    vm.pesquisarAcao(vm.idAcaoAvaliacao);
                });
            };

            var pesquisaTiposAcao = function () {
                consultaAcoesAvaliacaoService.consultarTiposAcao().then(function (resposta) {
                    vm.listaTiposAcao = resposta.data;
                });
            };

            var pesquisaStatusAcao = function () {
                consultaAcoesAvaliacaoService.consultarStatusAcao().then(function (resposta) {
                    vm.listaStatusAcao = resposta.data;
                });
            };

            var pesquisaTipoAcaoExecutada = function () {
                consultaAcoesAvaliacaoService.consultarTipoAcaoExecutada().then(function (resposta) {
                    vm.listaTipoAcaoExecutada = resposta.data;
                });
            };

            var pesquisaTiposCargo = function () {
                consultaAcoesAvaliacaoService.consultarTiposCargo().then(function (resposta) {
                    vm.listaTiposCargo = resposta.data;
                });
            };

            vm.pesquisarAcoesAvaliacaoLaudo = function (idLaudoAvaliacao) {
                consultaAcoesAvaliacaoService.consultarAcoesAvaliacaoLaudo(idLaudoAvaliacao)
                    .then(function (resp) {
                        vm.listaAcoes = resp.data;
                    });
            };

            vm.pesquisarAcoesAvaliacaoPvg = function (idLaudoAvaliacao) {
                consultaAcoesAvaliacaoService.consultarAcoesAvaliacaoPvg(idLaudoAvaliacao)
                    .then(function (resp) {
                        vm.listaAcoes = resp.data;
                    });
            };

            vm.pesquisarAcao = function (id) {
                consultaAcoesAvaliacaoService.consultarAcaoId(id)
                    .then(function (resp) {
                        vm.acaoAvaliacao = resp.data;
                        vm.laudoAvaliacao = angular.copy(resp.data.laudoAvaliacao);
                        vm.pvg = angular.copy(resp.data.pvg);
                        if (vm.laudoAvaliacao) {
                            vm.pesquisarAcoesAvaliacaoLaudo(vm.acaoAvaliacao.laudoAvaliacao.id);
                        } else if (vm.pvg) {
                            vm.pesquisarAcoesAvaliacaoPvg(vm.acaoAvaliacao.pvg.id);
                        }
                        vm.validar = true;
                        if (vm.acaoAvaliacao.decisao == true) {
                            vm.validar = true;
                        } else if (vm.acaoAvaliacao.decisao == false) {
                            vm.validar = false;

                        }
                    });
            };

            vm.redirecionarConsultarAcao = function () {
                $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');
            };

            vm.ripImovelLink = function (rip) {
                window.open('#/incorporacao/detalharImovel/' + rip, '_blank');
            };

            vm.redirecionarDetalharLaudo = function (laudo) {
                var route;

                if (laudo.tipoAvaliacao === 'LAUDO_AVALIACAO_LOCACAO') {
                    route = '#/avaliacao/detalharLaudoAvaliacaoLocacao/';
                } else if (laudo.tipoAvaliacao === 'LAUDO_RELATORIO_VALOR_REFERENCIA') {
                    route = '#/avaliacao/detalharRelatorioValorReferencia/';
                } else {
                    route = '#/avaliacao/detalharLaudoAvaliacao/';
                }

                window.open(route + laudo.ripImovel + '/' + laudo.id, '_blank');
            };

            vm.redirecionarDetalharPvg = function (id, rip) {
                if (vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_INCRA') {
                    window.open('#/avaliacao/detalharAvaliacaoPvgIncra/' + rip + '/' + id, '_blank');
                } else if (vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_VALOR_VENAL') {
                    window.open('#/avaliacao/detalharAvaliacaoPvgMunicipio/' + rip + '/' + id, '_blank');
                }
            };

            init();
        }]);
}());

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('validarAcoesAvaliacaoController',
            ["$scope", "$filter", "consultaAcoesAvaliacaoService", "usuarioAvaliacaoService", "$state", "$mdMedia", "$mdDialog", "$stateParams", "ComumFactory", function ($scope, $filter, consultaAcoesAvaliacaoService, usuarioAvaliacaoService,
                      $state, $mdMedia, $mdDialog, $stateParams, ComumFactory) {

                var vm = $scope;

                vm.topDirections = ['left'];
                vm.bottomDirections = ['down', 'right'];
                vm.isOpen = false;
                vm.availableModes = ['md-fling', 'md-scale'];
                vm.selectedMode = 'md-scale';
                vm.availableDirections = ['right'];
                vm.selectedDirection = 'right';

                vm.idAcaoAvaliacao = $stateParams.idAcaoAvaliacao;
                vm.acaoAvaliacao = {};
                vm.laudoAvaliacao = {};
                vm.listaTiposAcao = [];
                vm.listaStatusAcao = [];

                vm.listaAcoes = {};
                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == 'false' && vm.usuarioLogado.AVALIACAO_ACOES_CHEFIA == 'false' && vm.usuarioLogado.AVALIACAO_ACOES_TECNICO == 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                var init = function () {
                    buscarUsuarioLogado(function () {
                        pesquisaTiposAcao();
                        pesquisaStatusAcao();
                        pesquisaTipoAcaoExecutada();
                        pesquisaTiposCargo();
                        vm.pesquisarAcao(vm.idAcaoAvaliacao);
                    });
                };

                var pesquisaTiposAcao = function () {
                    consultaAcoesAvaliacaoService.consultarTiposAcao().then(function (resposta) {
                        vm.listaTiposAcao = resposta.data;
                    });
                };

                var pesquisaStatusAcao = function () {
                    consultaAcoesAvaliacaoService.consultarStatusAcao().then(function (resposta) {
                        vm.listaStatusAcao = resposta.data;
                    });
                };

                var pesquisaTipoAcaoExecutada = function () {
                    consultaAcoesAvaliacaoService.consultarTipoAcaoExecutada().then(function (resposta) {
                        vm.listaTipoAcaoExecutada = resposta.data;
                    });
                };

                vm.pesquisarAcoesAvaliacaoLaudo = function (idLaudoAvaliacao) {
                    consultaAcoesAvaliacaoService.consultarAcoesAvaliacaoLaudo(idLaudoAvaliacao)
                        .then(function (resp) {
                            vm.listaAcoes = resp.data;
                        });
                };

                var pesquisaTiposCargo = function () {
                    consultaAcoesAvaliacaoService.consultarTiposCargo().then(function (resposta) {
                        vm.listaTiposCargo = resposta.data;
                    });
                };

                vm.pesquisarAcoesAvaliacaoPvg = function (idPvg) {
                    consultaAcoesAvaliacaoService.consultarAcoesAvaliacaoPvg(idPvg)
                        .then(function (resp) {
                            vm.listaAcoes = resp.data;
                        });
                };

                vm.pesquisarAcoesAvaliacaoPvgSpu = function (idPvg) {
                    consultaAcoesAvaliacaoService.consultarAcoesAvaliacaoPvgSpu(idPvg)
                        .then(function (resp) {
                            vm.listaAcoes = resp.data;
                        });
                };

                vm.pesquisarAcao = function (id) {
                    consultaAcoesAvaliacaoService.consultarAcaoId(id)
                        .then(function (resp) {
                            vm.acaoAvaliacao = resp.data;
                            vm.laudoAvaliacao = angular.copy(resp.data.laudoAvaliacao);
                            vm.pvg = angular.copy(resp.data.pvg);
                            vm.pvgSpu = angular.copy(resp.data.pvgSpu);
                            if (vm.laudoAvaliacao) {
                                vm.pesquisarAcoesAvaliacaoLaudo(vm.acaoAvaliacao.laudoAvaliacao.id);
                            } else if (vm.pvg) {
                                vm.pesquisarAcoesAvaliacaoPvg(vm.acaoAvaliacao.pvg.id);
                            } else if (vm.pvgSpu) {
                                vm.pesquisarAcoesAvaliacaoPvgSpu(vm.acaoAvaliacao.pvgSpu.id);
                            }
                            vm.validar = true;
                            if (vm.acaoAvaliacao.decisao == true) {
                                vm.validar = true;
                            } else if (vm.acaoAvaliacao.decisao == false) {
                                vm.validar = false;

                            }
                        });
                };

                vm.salvarAcaoAvaliacao = function (gravarVisualizar) {
                    if (vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_LAUDO'
                        || vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_LAUDO_LOCACAO'
                        || vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_RELATORIO'
                        || vm.acaoAvaliacao.tipoAcaoAvaliacao === 'REVALIDACAO_LAUDO'
                        || vm.acaoAvaliacao.tipoAcaoAvaliacao === 'REVALIDACAO_RELATORIO') {
                        if (vm.acaoAvaliacao.decisao != null) {
                            vm.acaoAvaliacao.laudoAvaliacao = angular.copy(vm.laudoAvaliacao);
                            consultaAcoesAvaliacaoService.salvarAcaoAvaliacao(vm.acaoAvaliacao).then(function () {
                                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                if (gravarVisualizar) {
                                    var tipoParaRota = {
                                        'AVALIACAO_LAUDO_LOCACAO': 'avaliacao.DETALHAR_LAUDO_AVALIACAO_LOCACAO',
                                        'AVALIACAO_LAUDO': 'avaliacao.DETALHAR_LAUDO_AVALIACAO',
                                        'REVALIDACAO_LAUDO': 'avaliacao.DETALHAR_LAUDO_AVALIACAO',
                                        'AVALIACAO_RELATORIO': 'avaliacao.DETALHAR_RELATORIO_VALOR_REFERENCIA',
                                        'REVALIDACAO_RELATORIO': 'avaliacao.DETALHAR_RELATORIO_VALOR_REFERENCIA'
                                    };

                                    var route = tipoParaRota[vm.acaoAvaliacao.tipoAcaoAvaliacao];

                                    if (!route) {
                                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-tipo-laudo-avaliacao-desconhecido'));
                                        return;
                                    }

                                    $state.go(route, {
                                        rip: vm.acaoAvaliacao.laudoAvaliacao.ripImovel,
                                        idLaudo: vm.acaoAvaliacao.laudoAvaliacao.id
                                    });
                                } else {
                                    $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');
                                }
                            }, function (erro) {
                                ComumFactory.mostrarMensagemError(erro.data.message);
                            });
                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-erro-gravar-acao'));
                        }
                    } else if (vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_INCRA' || vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_VALOR_VENAL') {
                        if (vm.acaoAvaliacao.decisao != null) {
                            vm.acaoAvaliacao.pvg = angular.copy(vm.pvg);
                            consultaAcoesAvaliacaoService.salvarAcaoPvg(vm.acaoAvaliacao).then(function () {
                                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                if (gravarVisualizar) {
                                    if (vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_INCRA') {
                                        $state.go('avaliacao.DETALHAR_AVALIACAO_PVG_INCRA', {
                                            rip: vm.acaoAvaliacao.pvg.ripImovel,
                                            idPvg: vm.acaoAvaliacao.pvg.id
                                        });
                                    } else if (vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_VALOR_VENAL') {
                                        $state.go('avaliacao.DETALHAR_AVALIACAO_PVG_MUNICIPIO', {
                                            rip: vm.acaoAvaliacao.pvg.ripImovel,
                                            idPvg: vm.acaoAvaliacao.pvg.id
                                        });
                                    }
                                } else {
                                    $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');
                                }
                            });
                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-erro-gravar-acao'));
                        }
                    } else if (vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_PVG_SPU') {
                        if (vm.acaoAvaliacao.decisao != null) {
                            vm.acaoAvaliacao.pvgSpu = angular.copy(vm.pvgSpu);
                            consultaAcoesAvaliacaoService.salvarAcaoPvgSpu(vm.acaoAvaliacao).then(function () {
                                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                if (gravarVisualizar) {
                                    $state.go('avaliacao.DETALHAR_AVALIACAO_PVG_SPU', {
                                        rip: vm.acaoAvaliacao.pvgSpu.ripImovel,
                                        idPvg: vm.acaoAvaliacao.pvgSpu.id
                                    });

                                } else {
                                    $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');
                                }
                            });
                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-erro-gravar-acao'));
                        }
                    }
                };

                vm.redirecionarConsultarAcao = function () {
                    $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');
                };

                vm.ripImovelLink = function (rip) {
                    window.open('#/incorporacao/detalharImovel/' + rip, '_blank');
                };

                vm.changeValidarAcao = function (value) {
                    vm.acaoAvaliacao.justificativa = null;
                    vm.validar = value == true;
                };

                vm.redirecionarDetalharLaudo = function (laudo) {
                    var route;

                    if (laudo.tipoAvaliacao === 'LAUDO_AVALIACAO_LOCACAO') {
                        route = '#/avaliacao/detalharLaudoAvaliacaoLocacao/';
                    } else if (laudo.tipoAvaliacao === 'LAUDO_RELATORIO_VALOR_REFERENCIA') {
                        route = '#/avaliacao/detalharRelatorioValorReferencia/';
                    } else {
                        route = '#/avaliacao/detalharLaudoAvaliacao/';
                    }

                    window.open(route + laudo.ripImovel + '/' + laudo.id, '_blank');
                };

                vm.redirecionarDetalharPvg = function (id, rip) {
                    if (vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_INCRA') {
                        window.open('#/avaliacao/detalharAvaliacaoPvgIncra/' + rip + '/' + id, '_blank');
                    } else if (vm.acaoAvaliacao.tipoAcaoAvaliacao === 'AVALIACAO_VALOR_VENAL') {
                        window.open('#/avaliacao/detalharAvaliacaoPvgMunicipio/' + rip + '/' + id, '_blank');
                    }
                };

                init();
            }]);
}());
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('consultarAcoesAvaliacaoController',
            ["$scope", "$filter", "consultaAcoesAvaliacaoService", "$state", "usuarioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "$rootScope", "ComumFactory", function ($scope, $filter, consultaAcoesAvaliacaoService,
                      $state, usuarioAvaliacaoService, avaliacaoEscopoCompartilhadoService,$rootScope, ComumFactory) {

                var vm = $scope;

                vm.topDirections = ['left'];
                vm.bottomDirections = ['down', 'right'];
                vm.isOpen = false;
                vm.availableModes = ['md-fling', 'md-scale'];
                vm.selectedMode = 'md-scale';
                vm.availableDirections = ['right'];
                vm.selectedDirection = 'right';

                vm.filtroConsulta = {};
                vm.listaTiposAcao = [];
                vm.listaStatusAcao = [];

                vm.listaAcoes = {};
                vm.registroInicial = 1;
                vm.tamanhoLimite = 10;
                vm.totalItems = 0;

                vm.limparCamposPesquisa = function () {
                    vm.filtroConsulta = {};
                    vm.pesquisarAcoesAvaliacao();
                };

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE === 'false' && vm.usuarioLogado.AVALIACAO_ACOES_CHEFIA === 'false' && vm.usuarioLogado.AVALIACAO_ACOES_TECNICO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }

                        if (callback) {
                            callback();
                        }
                    });

                };

                $scope.validarDatas = function() {
                    if ($scope.filtroConsulta.dtInicio && $scope.filtroConsulta.dtFim) {
                        if ($scope.filtroConsulta.dtFim < $scope.filtroConsulta.dtInicio) {
                            $scope.erroDatas = $filter('translate')('mensagem-erro-data-invalida');
                            $scope.filtroConsulta.dtFim = null;
                        } else {
                            $scope.erroDatas = "";
                        }
                    }
                };

                var init = function () {
                    buscarUsuarioLogado(function () {
                        if (avaliacaoEscopoCompartilhadoService.getObjeto('filtroConsulta')) {
                            vm.filtroConsulta = avaliacaoEscopoCompartilhadoService.getObjeto('filtroConsulta');
                            avaliacaoEscopoCompartilhadoService.limparEscopo();
                        }
                        pesquisaTiposAcaoAvaliacaoList();
                        pesquisaTiposCargo();
                        pesquisaStatusAcao();
                        pesquisaTipoAcaoExecutada();
                        vm.pesquisarAcoesAvaliacao();
                    })

                    if ($rootScope.filtroConsultaAcoes) {
                        vm.filtroConsulta = angular.copy($rootScope.filtroConsultaAcoes);
                        vm.pesquisarAcoesAvaliacao();
                    }

                };

                var pesquisaTiposAcaoAvaliacaoList = function () {
                    consultaAcoesAvaliacaoService.consultarTiposAcao().then(function (resposta) {
                        var todosTipos = resposta.data;

                        // Tipos que realmente vêm do backend
                        var tiposPermitidosBackend = [
                            "AVALIACAO_LAUDO",
                            "AVALIACAO_RELATORIO",
                            "REVALIDACAO_LAUDO",
                            "REVALIDACAO_RELATORIO"
                        ];

                        // Lista final que será exibida no filtro
                        var listaFiltrada = {
                            "TODOS": "Todos"
                        };

                        // Adiciona tipos do backend permitidos
                        tiposPermitidosBackend.forEach(function (chave) {
                            if (todosTipos[chave]) {
                                listaFiltrada[chave] = todosTipos[chave];
                            }
                        });

                        // Seta no controller
                        vm.listaTiposAcao = listaFiltrada;
                    });
                };
                var pesquisaTiposCargo = function () {
                    consultaAcoesAvaliacaoService.consultarTiposCargo().then(function (resposta) {
                        vm.listaTiposCargo = resposta.data;
                    });
                };

                var pesquisaStatusAcao = function () {
                    consultaAcoesAvaliacaoService.consultarStatusAcao().then(function (resposta) {
                        vm.listaStatusAcao = resposta.data;
                    });
                };

                var pesquisaTipoAcaoExecutada = function () {
                    consultaAcoesAvaliacaoService.consultarTipoAcaoExecutada().then(function (resposta) {
                        vm.listaTipoAcaoExecutada = resposta.data;
                    });
                };

                vm.pesquisarAcoesAvaliacao = function () {
                    var tiposSelecionados = vm.filtroConsulta.tipoAcaoAvaliacaoList;

                    var tiposPermitidos = [
                        "AVALIACAO_LAUDO",
                        "AVALIACAO_RELATORIO",
                        "REVALIDACAO_LAUDO",
                        "REVALIDACAO_RELATORIO" // <- mesmo sendo hardcoded, ele precisa ser considerado aqui
                    ];

                    if (!tiposSelecionados || tiposSelecionados.length === 0 || tiposSelecionados.indexOf("TODOS") !== -1) {
                        vm.filtroConsulta.tipoAcaoAvaliacaoList = tiposPermitidos;
                    }

                    consultaAcoesAvaliacaoService.consultarAcoesAvaliacao(
                        vm.filtroConsulta,
                        (vm.registroInicial - 1),
                        vm.tamanhoLimite
                    ).then(function (resp) {
                        vm.listaAcoes = resp.data.content;
                        vm.totalItems = resp.data.totalElements;
                    });
                };

                vm.ripImovelLink = function (rip) {
                    window.open('#/incorporacao/detalharImovel/' + rip, '_blank');
                }

                vm.analisarAcaoTecnico = function (item) {
                    if (usuarioAvaliacaoService.mesmosUsuarios(item, vm.usuarioLogado)) {
                        return;
                    }

                    $state.go('avaliacao.VALIDAR_ACOES_AVALIACAO', {
                        idAcaoAvaliacao: item.id
                    });
                }

                vm.visualizarHistorico = function (id) {
                    $rootScope.filtroConsultaAcoes = angular.copy(vm.filtroConsulta);
                    $state.go('avaliacao.VISUALIZAR_HISTORICO_ACOES_AVALIACAO', {
                        idAcaoAvaliacao: id
                    });
                }

                vm.redirecionarDetalharLaudo = function (laudoAvaliacao) {
                    var url = null;

                    switch (laudoAvaliacao.tipoAvaliacao) {
                        case "LAUDO_AVALIACAO":
                            url = '#/avaliacao/detalharLaudoAvaliacao/';
                            break;
                        case "LAUDO_RELATORIO_VALOR_REFERENCIA":
                            url = '#/avaliacao/detalharRelatorioValorReferencia/';
                            break;
                        default:
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-tipo-laudo-avaliacao-desconhecido'));
                            return;
                    }

                    if (url) {
                        var fullUrl = url + laudoAvaliacao.ripImovel + '/' + laudoAvaliacao.id;
                        window.open(fullUrl, '_blank');
                    }
                };

                init();
            }]);
}());

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('assinarTermoResponsabilidadeRevalidacaoLaudoAvaliacaoController',
            ["$scope", "avaliacaoEscopoCompartilhadoService", "formularioAvaliacaoService", "$state", "usuarioAvaliacaoService", function($scope, avaliacaoEscopoCompartilhadoService,formularioAvaliacaoService, $state, usuarioAvaliacaoService) {

                var vm = $scope;

                vm.responsavel = {};

                vm.labelTermoResponsabilidade = 'label-certifico-informcoes-verdadeiras-arquivo-nota-tecnica-revalidacao-avaliacao';

                var init = function () {
                    vm.rip = avaliacaoEscopoCompartilhadoService.getObjeto('rip');
                    vm.idLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('idLaudo');
                    vm.laudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao');
                    vm.revalidacaoLaudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('revalidacaoLaudoAvaliacao');
                    vm.formularioRevalidarLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioRevalidarLaudo');
                    vm.orgao = vm.revalidacaoLaudoAvaliacao.orgao;
                    vm.unidade =  vm.revalidacaoLaudoAvaliacao.unidade;
                    vm.subunidade =  vm.revalidacaoLaudoAvaliacao.subunidade;

                    avaliacaoEscopoCompartilhadoService.limparEscopo();

                    if (!vm.rip && !vm.laudoAvaliacao) {
                        window.history.back();
                    }
                    buscarUsuarioLogado();
                };

                var buscarUsuarioLogado = function() {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function(resp) {
                        vm.responsavel = resp.data;
                    });
                };

                var recuperarDadosLaudoAvaliacao = function() {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('idLaudo', vm.idLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('revalidacaoLaudoAvaliacao', vm.revalidacaoLaudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioRevalidarLaudo', vm.formularioRevalidarLaudo);

                };

                vm.voltarTermoResponsabilidade = function() {
                    recuperarDadosLaudoAvaliacao();
                    $state.go('avaliacao.REVALIDAR_LAUDO_AVALIACAO', {
                        idLaudo: vm.idLaudo
                    });
                };

                vm.assinarTermoResponsabilidade = function() {
                    vm.revalidacaoLaudoAvaliacao.termoResponsabilidade = {
                        dataAssinatura: new Date(),
                        isAssinado: true
                    };
                    recuperarDadosLaudoAvaliacao();
                    $state.go('avaliacao.REVALIDAR_LAUDO_AVALIACAO', {
                        idLaudo: vm.idLaudo
                    });
                };

                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('assinarTermoResponsabilidadeLaudoAvaliacaoController',
            ["$scope", "avaliacaoEscopoCompartilhadoService", "formularioAvaliacaoService", "$state", "usuarioAvaliacaoService", function($scope, avaliacaoEscopoCompartilhadoService,formularioAvaliacaoService, $state, usuarioAvaliacaoService) {

                var vm = $scope;

                vm.responsavel = {};

                vm.labelTermoResponsabilidade = 'label-certifico-informcoes-verdadeiras-arquivo-laudo-avaliacao';

                var init = function () {
                    vm.rip = avaliacaoEscopoCompartilhadoService.getObjeto('rip');
                    vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                    vm.laudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao');
                    vm.listaBenfeitoriasImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriasImovel');
                    vm.unidadeAutonomaImovel = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaImovel');
                    vm.tipoValorLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('tipoValorLaudo');
                    vm.orgao = avaliacaoEscopoCompartilhadoService.getObjeto('orgao');
                    vm.unidade = avaliacaoEscopoCompartilhadoService.getObjeto('unidade');
                    vm.subunidade = avaliacaoEscopoCompartilhadoService.getObjeto('subunidade');
                    vm.detalharLaudoRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('detalharLaudoRelatorio');
                    avaliacaoEscopoCompartilhadoService.limparEscopo();

                    if (!vm.rip && !vm.laudoAvaliacao) {
                        window.history.back();
                    }
                    buscarUsuarioLogado();
                };

                var buscarUsuarioLogado = function() {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function(resp) {
                        vm.responsavel = resp.data;
                    });
                };

                var recuperarDadosLaudoAvaliacao = function() {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriasImovel', vm.listaBenfeitoriasImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaImovel', vm.unidadeAutonomaImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoValorLaudo', vm.tipoValorLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('orgao', vm.orgao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidade', vm.unidade);
                    avaliacaoEscopoCompartilhadoService.setObjetos('subunidade', vm.subunidade);
                };

                vm.voltarTermoResponsabilidade = function() {
                    recuperarDadosLaudoAvaliacao();
                    $state.go('avaliacao.CADASTRAR_LAUDO_AVALIACAO', {
                        rip: vm.rip
                    });
                };

                vm.voltarTermoResponsabilidadeDetalhar = function() {
                    $state.go('avaliacao.DETALHAR_LAUDO_AVALIACAO', {
                        'rip': vm.rip,
                        'idLaudo': vm.laudoAvaliacao.id
                    });
                };

                vm.assinarTermoResponsabilidade = function() {
                    vm.laudoAvaliacao.termoResponsabilidade = {
                        dataAssinatura: new Date(),
                        isAssinado: true
                    };
                    recuperarDadosLaudoAvaliacao();
                    $state.go('avaliacao.CADASTRAR_LAUDO_AVALIACAO', {
                        rip: vm.rip
                    });
                };

                init();
            }])

})();
/**
 * Created by samuel on 28/09/17.
 */
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('visualizarRevalidarLaudoAvaliacaoController',
            ["$scope", "$state", "$stateParams", "laudoAvaliacaoService", "usuarioAvaliacaoService", "ComumFactory", "$filter", "formularioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "imagemAvaliacaoService", "$sce", function ($scope, $state, $stateParams, laudoAvaliacaoService, usuarioAvaliacaoService, ComumFactory, $filter, formularioAvaliacaoService,
                      avaliacaoEscopoCompartilhadoService, imagemAvaliacaoService, $sce) {
                var vm = $scope;

                vm.revalidacaoLaudoAvaliacao = {};
                vm.revalidacaoLaudoAvaliacao.responsaveis = [];
                vm.revalidacaoLaudoAvaliacao.arquivosLaudoRevalidacao = [];
                vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio = null;

                vm.UPLOAD_ARQUIVO = 'UPLOAD_ARQUIVO';
                vm.PREENCHER_SISTEMA = 'PREENCHER_SISTEMA';

                var init = function () {
                    vm.formularioRevalidarLaudo = {};
                    vm.idLaudo = $stateParams.idLaudo;

                    vm.rip = avaliacaoEscopoCompartilhadoService.getObjeto('rip');
                    vm.idLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('idLaudo');
                    vm.laudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao');
                    vm.revalidacaoLaudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('revalidacaoLaudoAvaliacao');
                    vm.formularioRevalidarLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioRevalidarLaudo');
                    vm.detalharLaudoRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('detalharLaudoRelatorio');
                    avaliacaoEscopoCompartilhadoService.limparEscopo();

                    if(vm.revalidacaoLaudoAvaliacao.tipoArquivo === 'UPLOAD_ARQUIVO') {
                        angular.forEach(vm.revalidacaoLaudoAvaliacao.anexos, function (anexo) {
                            anexo.sce = $sce.trustAsResourceUrl(vm.gerarImagem(anexo.arquivo.binario, anexo.arquivo.contentType));
                        });
                    }

                };


                var recuperarDadosLaudoAvaliacao = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('idLaudo', vm.idLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('revalidacaoLaudoAvaliacao', vm.revalidacaoLaudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioRevalidarLaudo', vm.formularioRevalidarLaudo);

                };

                vm.voltarRevalidarLaudo = function () {
                    recuperarDadosLaudoAvaliacao();
                    $state.go('avaliacao.REVALIDAR_LAUDO_AVALIACAO', {
                        idLaudo: vm.idLaudo
                    });
                };

                vm.voltarDetalharLaudo = function () {
                    if (vm.laudoAvaliacao.tipoAvaliacao === 'LAUDO_AVALIACAO') {
                        $state.go('avaliacao.DETALHAR_LAUDO_AVALIACAO', {
                            'rip': vm.rip,
                            'idLaudo': vm.laudoAvaliacao.id
                        });
                    }else{
                        $state.go('avaliacao.DETALHAR_LAUDO_AVALIACAO_LOCACAO', {
                            'rip': vm.rip,
                            'idLaudo': vm.laudoAvaliacao.id
                        });
                    }
                };

                vm.gerarImagem = function(base64, contentType) {
                    return imagemAvaliacaoService.gerarPreviewImagem(base64, contentType);
                };

                init();

            }])

})();
/**
 * Created by samuel on 28/09/17.
 */
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('revalidarLaudoAvaliacaoController',
            ["$scope", "$state", "$stateParams", "laudoAvaliacaoService", "usuarioAvaliacaoService", "ComumFactory", "$filter", "$mdDialog", "formularioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "imovelAvaliacaoService", "documentoSeiService", "comumAvaliacaoService", function ($scope, $state, $stateParams, laudoAvaliacaoService, usuarioAvaliacaoService, ComumFactory, $filter, $mdDialog, formularioAvaliacaoService,
                      avaliacaoEscopoCompartilhadoService, imovelAvaliacaoService, documentoSeiService, comumAvaliacaoService) {
                var vm = $scope;

                var QTD_MAXIMA_REVALIDACAO = 2;

                vm.revalidacaoLaudoAvaliacao = {};
                vm.revalidacaoLaudoAvaliacao.termoResponsabilidade = {};
                vm.revalidacaoLaudoAvaliacao.responsaveis = [];
                vm.revalidacaoLaudoAvaliacao.arquivosLaudoRevalidacao = [];
                vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio = null;

                vm.listaTipoNotaTecnicaHomologacao = [];

                vm.UPLOAD_ARQUIVO = 'UPLOAD_ARQUIVO';
                vm.DOCUMENTO_SEI = 'DOCUMENTO_SEI';
                vm.DOZE_MESES = 12;
                vm.VINTE_E_QUATRO_MESES = 24;
                vm.VALIDAR_12OU24_MESES = 'VALIDAR_12OU24_MESES';

                vm.inicioEstendida;
                vm.finalEstendida;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                var init = function () {
                    vm.formularioRevalidarLaudo = {};
                    vm.idLaudo = $stateParams.idLaudo;
                    vm.getUsuarioLogado();

                    laudoAvaliacaoService.consultarLaudoAvaliacaoId(vm.idLaudo).then(function (resp) {
                        vm.laudoAvaliacao = resp.data;

                        if (vm.laudoAvaliacao.revalidacoes != null && vm.laudoAvaliacao.revalidacoes.length > 0) {

                            if(!vm.podeRevalidar()) {
                                ComumFactory.mostrarMensagemError($filter('translate')('menssagem-limite-revalidacoes'));
                                $state.go('avaliacao.CONSULTAR_DETALHAR_IMOVEL_AVALIACAO', {
                                    rip: vm.laudoAvaliacao.ripImovel
                                });
                            }

                            var ultimaRevalidacao = laudoAvaliacaoService.getUltimaRevalidacao(vm.laudoAvaliacao);
                            if (vm.laudoAvaliacao.situacaoLaudo=='REVALIDACAO_PREENCHIMENTO') {
                                vm.revalidacaoLaudoAvaliacao = angular.copy(ultimaRevalidacao);
                                vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorioTemp = vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio;
                                vm.calcularDatasEstendidas();
                            }
                        }

                        vm.rip = vm.laudoAvaliacao.ripImovel;
                        buscarDadosImovel();
                        vm.buscarListaUnidades();

                    });

                    buscarTipoNotaTecnicaHomologacao();
                };

                vm.podeRevalidar = function () {
                    if (vm.laudoAvaliacao.situacaoLaudo == 'REVALIDACAO_PREENCHIMENTO' || vm.laudoAvaliacao.isRevalidar) {
                        return true;
                    }
                    return false;
                }

                vm.getUsuarioLogado = function () {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (result) {
                        vm.usuarioLogado = result.data;

                    });
                };

                var buscarTipoNotaTecnicaHomologacao = function () {
                    laudoAvaliacaoService.buscarTipoNotaTecnicaRevalidacaoLaudo().then(function (resp) {
                        vm.listaTipoNotaTecnicaHomologacao = resp;
                    });
                };

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                vm.redirecionarDetalharLaudoAvaliacao = function () {
                    var url = $state.href('avaliacao.DETALHAR_LAUDO_AVALIACAO', {
                        rip: vm.rip,
                        idLaudo: vm.idLaudo
                    });
                    window.open(url, '_blank');
                };

                var validarResponsaveisTecnicosInformados = function () {
                    var responsaveisTecnicosValido = true;
                    if (vm.revalidacaoLaudoAvaliacao.responsaveis.length === 0) {
                        responsaveisTecnicosValido =  false;
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-necessario-informar-responsavel-tecnico'));
                    }
                    angular.forEach(vm.revalidacaoLaudoAvaliacao.responsaveis, function (responsavelTecnico) {
                        if (!responsavelTecnico.crea) {
                            responsaveisTecnicosValido = false;
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-necessario-informar-crea-responsavel'));
                        }
                    });

                    return responsaveisTecnicosValido;
                };

                vm.visualizarDocumentoRevalidacao = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('idLaudo', vm.idLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('revalidacaoLaudoAvaliacao', vm.revalidacaoLaudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioRevalidarLaudo', vm.formularioRevalidarLaudo);

                    $state.go('avaliacao.VISUALIZAR_DOCUMENTO_REVALIDAR_LAUDO_AVALIACAO');
                };

                vm.revalidarLaudoAvaliacao = function () {
                    vm.validarFormulario(vm.formularioRevalidarLaudo);

                    if (!vm.formularioRevalidarLaudo.$valid) {
                        return;
                    }

                    if(!validarResponsaveisTecnicosInformados()) {
                        return;
                    }

                    if (vm.revalidacaoLaudoAvaliacao.arquivosLaudoRevalidacao.length == 0) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-adicionar-laudo-avaliacao'));
                        return;
                    }

                    vm.ajustarDatasAnexo(vm.revalidacaoLaudoAvaliacao.anexos);

                    laudoAvaliacaoService.revalidarLaudoAvaliacaoRevalidacao(vm.idLaudo, vm.revalidacaoLaudoAvaliacao)
                        .then(function () {
                            if (vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio === vm.PREENCHER_SISTEMA && vm.verificarHomologador()) {
                                laudoAvaliacaoService.assinarRevalidarLaudoAvaliacaoRevalidacao(vm.idLaudo).then(function () {
                                });
                            }
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                            $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                ripDetalhar: vm.rip
                            });
                        });
                };

                vm.salvarComoRascunhoRevalidacao = function () {

                    vm.ajustarDatasAnexo(vm.revalidacaoLaudoAvaliacao.anexos);

                    laudoAvaliacaoService.salvarComoRascunhoRevalidacao(vm.idLaudo, vm.revalidacaoLaudoAvaliacao)
                        .then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                            $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                ripDetalhar: vm.rip
                            });
                        });
                };

                vm.ajustarDatasAnexo = function(anexos) {
                    angular.forEach(anexos, function (anexo) {
                        anexo.dtArquivo = new Date(anexo.dtArquivo);
                    });
                }

                vm.verificarHomologador = function () {
                    for (var i in vm.revalidacaoLaudoAvaliacao.responsaveis) {
                        if (vm.revalidacaoLaudoAvaliacao.responsaveis[i]) {
                            if (vm.revalidacaoLaudoAvaliacao.responsaveis[i].cpf === vm.usuarioLogado.cpf) {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                vm.limparTipoRevalidacao = function () {
                    if (vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorioTemp === vm.PREENCHER_SISTEMA && vm.revalidacaoLaudoAvaliacao.responsaveis.length > 0) {
                        verificarListaHomologadores(function () {
                            if (vm.homologadoresRemovidos.length > 0) {
                                var confirm = $mdDialog.confirm()
                                    .title("Atenção!")
                                    .htmlContent($filter('translate')('mensagem-responsaveis-revalidacao-removidos-nao-usuarios-sistema') + "<br>" +
                                        vm.listCpfshomologadoresRemovidos +
                                        $filter('translate')('mensagem-deseja-alterar-tipo-revalidacao'))
                                    .ok("Confirmar")
                                    .cancel("Cancelar");

                                $mdDialog.show(confirm).then(function () {
                                    vm.revalidacaoLaudoAvaliacao.responsaveis = angular.copy(vm.homologadoresSistema);
                                    vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio = vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorioTemp;
                                    vm.revalidacaoLaudoAvaliacao.analise = null;
                                    vm.revalidacaoLaudoAvaliacao.sumarioExecutivo = null;
                                    vm.revalidacaoLaudoAvaliacao.consideracoesFinais = null;
                                    vm.revalidacaoLaudoAvaliacao.arquivosLaudoRevalidacao = null;
                                    vm.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei = {};
                                }, function () {
                                    vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorioTemp = vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio;
                                });
                            } else {
                                vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio = vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorioTemp;
                                vm.revalidacaoLaudoAvaliacao.analise = null;
                                vm.revalidacaoLaudoAvaliacao.sumarioExecutivo = null;
                                vm.revalidacaoLaudoAvaliacao.consideracoesFinais = null;
                                vm.revalidacaoLaudoAvaliacao.arquivosLaudoRevalidacao = null;
                                vm.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei = {};
                            }
                        });
                    } else {
                        vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio = vm.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorioTemp;
                        vm.revalidacaoLaudoAvaliacao.analise = null;
                        vm.revalidacaoLaudoAvaliacao.sumarioExecutivo = null;
                        vm.revalidacaoLaudoAvaliacao.consideracoesFinais = null;
                        vm.revalidacaoLaudoAvaliacao.arquivosLaudoRevalidacao = null;
                        vm.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei = {};
                    }
                };

                var verificarListaHomologadores = function (callback) {
                    vm.listCpfshomologadoresRemovidos = "";
                    vm.homologadoresSistema = [];
                    vm.homologadoresRemovidos = [];
                    vm.index = [];
                    angular.forEach(vm.revalidacaoLaudoAvaliacao.responsaveis, function (homologador, index) {
                        usuarioAvaliacaoService.getUsuarioSistema(homologador.cpf).then(function (resp) {
                            vm.index.pop();
                            if (resp.data === "") {
                                vm.listCpfshomologadoresRemovidos += $filter('cpfcnpj')(homologador.cpf) + "<br>";
                                vm.homologadoresRemovidos.push(homologador);
                            } else {
                                vm.homologadoresSistema.push(homologador);
                            }

                            if (vm.index.length === 0) {
                                if (callback) {
                                    callback();
                                }
                            }
                        });

                        vm.index.push(index);
                    });
                };

                var buscarListaOrgaos = function () {
                    usuarioAvaliacaoService.getOrgaos().then(function (resp) {
                        vm.listaOrgaos = resp.data;
                        if (vm.listaOrgaos.length === 1) {
                            vm.revalidacaoLaudoAvaliacao.orgao = vm.listaOrgaos[0];
                        }
                        vm.buscarListaUnidades();
                    });
                };

                vm.buscarListaUnidades = function () {
                    usuarioAvaliacaoService.getUnidadesUsuario().then(function (resp) {
                        vm.listaUnidades = resp.data;
                        if (vm.listaUnidades.length === 1) {
                            vm.laudoAvaliacao.unidade = vm.listaUnidades[0];
                        }
                    });
                };

                vm.changeDocumentoSei = function () {
                    vm.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.tipoEnviarDocumento = null;
                    vm.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei = null;
                };

                vm.changeNumeroSei = function () {
                    vm.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei = null;
                };

                vm.validarLaudoAvaliacaoNumeroSeiProcessoSei = function () {
                    if (vm.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei) {
                        documentoSeiService.recuperarProcedimentoSei(vm.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei, vm.imovel.enderecamento.uf).then(function () {

                        }, function (erro) {
                            vm.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei = null;
                            ComumFactory.mostrarMensagemError(erro.data.message);
                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('menssagem-numero-processo-sei-invalido'));
                    }

                };

                vm.limparAnexos = function () {
                    vm.revalidacaoLaudoAvaliacao.anexos = null;
                };

                var buscarDadosImovel = function () {
                    imovelAvaliacaoService.consultarImovelPorRip(vm.rip).then(function (resposta) {
                        vm.imovel = resposta.data;
                    });
                };

                vm.calcularDatasEstendidas = function () {
                    if (vm.revalidacaoLaudoAvaliacao.mesesRevalidacao == null)
                        return;

                    vm.inicioEstendida = vm.laudoAvaliacao.inicioPeriodo;
                    vm.finalEstendida = new Date(vm.laudoAvaliacao.finalPeriodo);
                    if (vm.revalidacaoLaudoAvaliacao.mesesRevalidacao == vm.DOZE_MESES){
                        vm.finalEstendida.setFullYear(vm.finalEstendida.getFullYear() + 1);
                    } else {
                        vm.finalEstendida.setFullYear(vm.finalEstendida.getFullYear() + 2);
                    }

                };

                init();

            }])

})();

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('assinarRevalidarLaudoAvaliacaoController',
            ["$scope", "$stateParams", "laudoAvaliacaoService", "$state", "ComumFactory", "$filter", "imagemAvaliacaoService", "$sce", "$mdDialog", function($scope, $stateParams, laudoAvaliacaoService, $state, ComumFactory, $filter, imagemAvaliacaoService, $sce, $mdDialog) {

                var vm = $scope;

                vm.laudoAvaliacao = {};
                vm.revalidacaoLaudoAvaliacao = {};

                var init = function() {
                    vm.idLaudo = $stateParams.idLaudo;

                    laudoAvaliacaoService.consultarLaudoAvaliacaoId(vm.idLaudo).then(function(resp) {
                        vm.laudoAvaliacao = resp.data;
                        vm.revalidacaoLaudoAvaliacao = angular.copy(vm.laudoAvaliacao.revalidacaoLaudoAvaliacao);

                        angular.forEach(vm.revalidacaoLaudoAvaliacao.anexos, function(anexo) {
                            anexo.sce = $sce.trustAsResourceUrl(vm.gerarImagem(anexo.arquivo.binario, anexo.arquivo.contentType));
                        });
                    });
                };

                vm.redirecionarDetalharImovelAvaliacao = function() {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.laudoAvaliacao.ripImovel
                    });
                };

                vm.recusarRevalidarAssinaturaLaudoAvaliacao = function() {
                    var confirm = $mdDialog.confirm({focusOnOpen: false})
                        .title($filter('translate')('label-titulo-confirmacao'))
                        .textContent($filter('translate')('mensagem-recusar-revalidacao'))
                        .ok($filter('translate')('btn-sim'))
                        .cancel($filter('translate')('btn-nao'));
                    $mdDialog.show(confirm).then(function () {
                        laudoAvaliacaoService.recusarAssinarRevalidarLaudoAvaliacao(vm.idLaudo).then(function() {
                            $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                ripDetalhar: vm.laudoAvaliacao.ripImovel
                            });
                        });
                    }, function () {

                    });

                };

                vm.assinarRevalidarLaudoAvaliacao = function() {
                    laudoAvaliacaoService.assinarRevalidarLaudoAvaliacaoRevalidacao(vm.idLaudo).then(function() {
                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-assinatura-registrada-sucesso'));
                        $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                            ripDetalhar: vm.laudoAvaliacao.ripImovel
                        });
                    });
                };


                vm.gerarImagem = function(base64, contentType) {
                    return imagemAvaliacaoService.gerarPreviewImagem(base64, contentType);
                };

                init();

            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('desvincularAvaliacaoPvgSpuController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "comumAvaliacaoService", "$state", "formularioAvaliacaoService", "$filter", "pvgSpuService", "ComumFactory", function ($scope, laudoAvaliacaoService, $stateParams, comumAvaliacaoService, $state, formularioAvaliacaoService,
                      $filter, pvgSpuService, ComumFactory) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.pvgSpu = {};

                var init = function () {
                    vm.rip = $stateParams.rip;
                    vm.dataMostrar = $filter('date')(new Date(), 'dd/MM/yyyy');
                    comumAvaliacaoService.usuarioLogado().then(function (resp) {
                        vm.pvgSpu.responsavelCancelamento = resp.data.responsavelCancelamento;
                    });

                    pesquisarMotivosCancelar();
                    pesquisarTrechosSelecionados();
                };

                var pesquisarMotivosCancelar = function () {
                    comumAvaliacaoService.consultarMotivosCancelar()
                        .then(function (resp) {
                            vm.motivosCancelar = resp.data;
                        });
                };

                var pesquisarTrechosSelecionados = function () {
                    pvgSpuService.consultarTrechosSelecionados(vm.rip).then(function (resp) {
                        vm.trechos = resp.data;
                    });
                };

                vm.desvincular = function (formularioDesvincularPvg) {
                    vm.validarFormulario(formularioDesvincularPvg);

                    if (!formularioDesvincularPvg.$valid) {
                        return;
                    }

                    vm.pvgSpu.ripImovel = vm.rip;

                    pvgSpuService.desvincularAvaliacaoPvgSpu(vm.pvgSpu).then(function () {
                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-dados-desvinculados-sucesso'));
                        $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                            ripDetalhar: vm.rip
                        });
                    });

                };

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cadastrarAvaliacaoPvgSpuController',
            ["$scope", "$mdDialog", "comumAvaliacaoService", "$stateParams", "$state", "$timeout", "trechoAvaliacaoService", "$filter", "ComumFactory", "pvgSpuService", "formularioAvaliacaoService", "imovelAvaliacaoService", "logradouroPvgAvaliacaoService", "arquivoAvaliacaoService", function ($scope, $mdDialog, comumAvaliacaoService, $stateParams, $state, $timeout, trechoAvaliacaoService,
                      $filter, ComumFactory, pvgSpuService, formularioAvaliacaoService, imovelAvaliacaoService, logradouroPvgAvaliacaoService, arquivoAvaliacaoService) {

                var vm = $scope;

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                vm.detalharPvgSpu = false;
                vm.rip = $stateParams.rip;

                vm.logradourosSiapa = [];
                vm.listaTrechos = [];
                vm.isCepGenerico = false;
                vm.isTrechoSelecionado = false;
                vm.pvgSpu = {};

                vm.logradouroPvgSelecionado = {};
                vm.showAvaliacoesTerrenos = false;
                vm.isEditarFator = false;
                vm.isEditar = false;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                var init = function () {
                    comumAvaliacaoService.buscarSituacoesAlteracaoAvaliacao().then(function (resp) {
                        vm.motivoAvaliacoes = resp.data;
                    });
                    comumAvaliacaoService.consultarMotivosCancelar().then(function (resp) {
                        vm.motivosCancelar = resp.data;
                    });
                    verficarAvaliacoesPvgSpu();

                    if ($stateParams.idPvg) {
                        vm.idPvg = $stateParams.idPvg;
                        pvgSpuService.consultarPorIdPvgSpu(vm.idPvg).then(function (resp) {
                            vm.pvgSpu = resp.data;
                            vm.pvgSpu.dataInicial = comumAvaliacaoService.recuperarData(vm.pvgSpu.dataInicial);
                            vm.logradouroPvgSelecionado = angular.copy(vm.pvgSpu.trecho.logradouroPvg);
                            vm.logradouroPvgSelecionado.trechos = [vm.pvgSpu.trecho];
                            if(vm.pvgSpu.motivoCancelamento){
                                vm.pvgSpu.motivoCancelamento = vm.motivosCancelar[vm.pvgSpu.motivoCancelamento];
                            }


                            vm.buscarTrechosPorLogradouro(function () {
                                for(var i = 0; i < vm.listaTrechos.length; i++){
                                    if(vm.pvgSpu.trecho.id ===  vm.listaTrechos[i].id){
                                        vm.pvgSpu.trecho = vm.listaTrechos[i];
                                        if($state.current.name.indexOf('EDITAR_FATORES_AVALIACAO_PVG_SPU') != -1){
                                            vm.trechoSelecionado();
                                        }
                                        break
                                    }
                                }
                            });

                            if($state.current.name.indexOf('EDITAR_FATORES_AVALIACAO_PVG_SPU') != -1){
                                vm.isEditarFator = true;
                                vm.mostrarMsgfator = false;

                                if(vm.pvgSpu.fatorCorretivoTotal > 2.5){
                                    vm.pvgSpu.fatorCorretivoTotal = 2.5;
                                    vm.mostrarMsgfator = true;
                                }else if(vm.pvgSpu.fatorCorretivoTotal  < 0.4){
                                    vm.pvgSpu.fatorCorretivoTotal = 0.4;
                                    vm.mostrarMsgfator = true;
                                }

                            }else{
                                vm.detalharPvgSpu = true;
                            }

                            if(vm.pvgSpu.fatores && vm.pvgSpu.fatores.length > 0){
                                vm.pvgSpu.adicionarFatores = true;
                            }

                            setTimeout(function() {
                                $("md-table-container tbody tr:not(md-table-container tbody tr:nth-last-child(2)) .btn-ver-mais").click();
                            }, 100)

                        });
                    } else {
                        pvgSpuService.buscarHistorioAvaliacaoPvgSpu(vm.rip, (vm.registroInicial - 1), vm.tamanhoLimite).then(function (resp) {
                            vm.historicoAvaliacoesPvgsSpu = resp.data.content;
                            vm.totalItems = resp.data.totalElements;
                        });

                        imovelAvaliacaoService.consultarImovelPorRip(vm.rip).then(function (resposta) {
                            vm.imovel = resposta.data;
                            vm.pvgSpu.ufImovel = vm.imovel.enderecamento.uf;

                            definirAreaTerreno();
                        });
                    }
                };

                var verficarAvaliacoesPvgSpu = function () {
                    pvgSpuService.verficarAvaliacoesPvgSpu(vm.rip, (vm.registroInicial - 1), vm.tamanhoLimite).then(function (resp) {
                        vm.showAvaliacoesTerrenos = resp.data.totalElements > 0;
                    });
                };

                vm.changeAdicionarFator = function(){
                    if(vm.isEditarFator){
                        if(!vm.pvgSpu.adicionarFatores){
                            if(vm.pvgSpu.fatores && vm.pvgSpu.fatores.length > 0){
                                var confirm = $mdDialog.confirm({focusOnOpen: false})
                                    .textContent($filter('translate')('mensagem-remover-fatores-incluidos'))
                                    .ok($filter('translate')('btn-sim'))
                                    .cancel($filter('translate')('btn-nao'));
                                $mdDialog.show(confirm).then(function () {
                                    vm.calcularFatorCorretivoTotal();
                                    vm.limparFator();
                                    vm.pvgSpu.fatores = null;
                                }, function () {
                                    vm.pvgSpu.adicionarFatores = true;
                                    $mdDialog.cancel();
                                });
                            }
                        }
                    }
                };

                vm.$watch('imovel.enderecamento.cep', function (cep) {
                    if (cep !== undefined && cep !== "") {
                        vm.isCepGenerico = false;
                        if (verificaCEPGenerico(cep)) {
                            vm.isCepGenerico = true;
                        }

                        if (vm.isCepGenerico) {
                            logradouroPvgAvaliacaoService.buscarListaLogradourosPorCepGenerico(cep).then(function (resp) {
                                vm.logradourosSiapa = resp.data;
                            }, function (erro) {
                                tratarErroBuscarLogradouroPvg(erro);
                            });
                        } else {
                            logradouroPvgAvaliacaoService.buscarLogradouroPorCepNaoGenerico(cep).then(function (resp) {
                                vm.logradouroPvgSelecionado = (resp.data ? (resp.data && resp.data.length > 0 ? resp.data[0] : resp.data) : null);
                                vm.buscarTrechosPorLogradouro();
                            }, function (erro) {
                                tratarErroBuscarLogradouroPvg(erro);
                            });
                        }
                    }
                });

                var tratarErroBuscarLogradouroPvg = function (erro) {
                    vm.detalharPvgSpu = true;
                    if (erro.data.status == 400) {
                        confirm(erro.data.message);
                    } else if (erro.data.status == 401) {
                        ComumFactory.mostrarMensagemError(erro.data.message);
                    }
                };

                var verificaCEPGenerico = function (cep) {
                    if (cep.endsWith("000")) {
                        return true;
                    } else {
                        return false;
                    }
                };

                vm.buscarTrechosPorLogradouro = function (callback) {
                    vm.listaTrechos = [];
                    trechoAvaliacaoService.buscarTrechosPorIdLogradouro(vm.logradouroPvgSelecionado.id).then(function (resp) {
                        vm.listaTrechos = resp.data;
                        if(callback){
                            callback();
                        }
                    }, function (erro) {
                        ComumFactory.mostrarMensagemError(erro.data.message);
                    });
                };

                vm.trechoSelecionado = function () {
                    vm.isTrechoSelecionado = false;
                    if (vm.pvgSpu.trecho && vm.pvgSpu.trecho != '') {
                        preencherDadosTrechoSelecionado();
                        vm.isTrechoSelecionado = true;
                    }
                };

                vm.fecharDialogAvaliacaoPvgSpu = function () {
                    $mdDialog.cancel();
                };

                var preencherDados = function () {
                    if( vm.areaTerreno){
                        vm.pvgSpu.areaFracaoTerreno = vm.areaTerreno;
                    }

                    vm.pvgSpu.ripImovel = vm.rip;
                    if (vm.pvgSpu.trecho) {
                        vm.pvgSpu.trecho.dataCriacao = setarDate(vm.pvgSpu.trecho.dataCriacao);
                        vm.pvgSpu.trecho.ultimaAtualizacaoAvaliacao = setarDate(vm.pvgSpu.trecho.dataAtualizacao);
                        vm.pvgSpu.trecho.dataCancelamento = setarDate(vm.pvgSpu.trecho.dataCancelamento);

                        if (vm.logradouroPvgSelecionado) {
                            vm.pvgSpu.trecho.logradouroPvg = vm.logradouroPvgSelecionado;
                            vm.pvgSpu.trecho.logradouroPvg.dataCriacao = setarDate(vm.pvgSpu.trecho.logradouroPvg.dataCriacao);
                            vm.pvgSpu.trecho.logradouroPvg.dataAtualizacao = setarDate(vm.pvgSpu.trecho.logradouroPvg.dataAtualizacao);
                            vm.pvgSpu.trecho.logradouroPvg.dataCancelamento = setarDate(vm.pvgSpu.trecho.logradouroPvg.dataCancelamento);
                            vm.pvgSpu.trecho.logradouroPvg.trechos = undefined;
                        }
                        vm.calcularValorTerreno();
                    }
                };

                var preencherDadosTrechoSelecionado = function () {
                    vm.pvgSpu.idTrecho = vm.pvgSpu.trecho.id;
                    vm.pvgSpu.valorAvaliacao = copiar(vm.pvgSpu.trecho.avaliacoes[vm.pvgSpu.trecho.avaliacoes.length - 1].valor);
                    vm.pvgSpu.ultimaAtualizacaoAvaliacao = copiarData(vm.pvgSpu.trecho.dataAtualizacao);
                    vm.pvgSpu.dataInicioAvaliacao = copiarData(vm.pvgSpu.trecho.dataInicioAvaliacao);
                    vm.pvgSpu.dataFimAvaliacao = copiarData(vm.pvgSpu.trecho.dataFimAvaliacao);
                    vm.pvgSpu.dataPrimeiraAvaliacao = copiarData(vm.pvgSpu.trecho.dataPrimeiraAvaliacao);
                    vm.calcularValorTerreno();

                };

                var copiar = function (valor) {
                    return angular.copy(valor);
                };

                var copiarData = function (data) {
                    return copiar(setarDate(data));
                };

                var setarDate = function (date) {
                    return date ? new Date(date) : undefined;
                };

                var validarDatasPorDataAtual = function () {
                    if (vm.pvgSpu.dataInicial > new Date()) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-posterior-data-atual'));
                        throw $filter('translate')('mensagem-data-posterior-data-atual');
                    }
                };

                vm.salvarAvaliacaoPvgSpu = function (formularioPVGSpu) {
                    vm.validarFormulario(formularioPVGSpu);

                    if(vm.pvgSpu.adicionarFatores){
                        if(!vm.pvgSpu.fatores || vm.pvgSpu.fatores < 1){
                            ComumFactory.mostrarMensagemError("Fatores não informados");
                        }
                    }

                    if (!formularioPVGSpu.$valid) {
                        return;
                    }

                    preencherDados();
                    validarDatasPorDataAtual();
                    if (vm.pvgSpu.motivo === 'ERRO') {
                        var confirm = $mdDialog.confirm({focusOnOpen: false})
                            .textContent($filter('translate')('mensagem-cancelar-trechos-vinculados'))
                            .ok($filter('translate')('btn-sim'))
                            .cancel($filter('translate')('btn-nao'));
                        $mdDialog.show(confirm).then(function () {
                            pvgSpuService.salvarAvaliacaoPvgSpu(vm.pvgSpu).then(function () {
                                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                    ripDetalhar: vm.rip
                                });
                            });
                        }, function () {
                            $mdDialog.cancel();
                        });
                    } else {
                        pvgSpuService.salvarAvaliacaoPvgSpu(vm.pvgSpu)
                            .then(function () {
                                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                    ripDetalhar: vm.rip
                                });
                            });
                    }
                };

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                var definirAreaTerreno = function () {
                    if (vm.imovel.proprietarioTitular) {
                        if ((vm.imovel.proprietarioTitular.idProprietarioReal && vm.imovel.proprietarioTitular.idProprietarioReal != '1')
                            || !vm.imovel.proprietarioTitular.uniaoParcialmente) {
                            /*area total*/
                            if (vm.imovel.terreno.areaTotalCadastral != 0 &&
                                vm.imovel.terreno.areaTotalCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCadastral;
                            }
                        } else if ((vm.imovel.tipoImovel.descricao == 'Unidade autônoma' &&
                            !vm.imovel.proprietarioTitular.uniaoParcialmente) ||
                            ((vm.imovel.proprietarioTitular.idProprietarioReal && vm.imovel.proprietarioTitular.idProprietarioReal != '1'))) {
                            /*Fração Ideal do Imóvel*/
                            if (vm.imovel.terreno.areaTotalCadastral != 0 &&
                                vm.imovel.terreno.areaTotalCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCadastral;
                            }
                        } else if (vm.imovel.proprietarioTitular.uniaoParcialmente &&
                            vm.imovel.tipoImovel.descricao != 'Unidade autônoma') {
                            /*Area da Uniao*/
                            if (vm.imovel.terreno.areaUniaoCadastral != 0 &&
                                vm.imovel.terreno.areaUniaoCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCadastral;
                            }
                        } else if (vm.imovel.proprietarioTitular.uniaoParcialmente &&
                            vm.imovel.tipoImovel.descricao == 'Unidade autônoma') {
                            /*Fração Ideal da União*/
                            if (vm.imovel.terreno.areaUniaoCadastral != 0 &&
                                vm.imovel.terreno.areaUniaoCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCadastral;
                            }
                        }
                    }
                };

                vm.limparFator = function(){
                    vm.fator = {};
                };

                vm.adicionarFator = function(fator) {
                    if (fator.descricao && fator.valor && fator.justificativa) {
                        if (!vm.pvgSpu.fatores) {
                            vm.pvgSpu.fatores = [];
                        }

                        vm.pvgSpu.fatores.push(fator);
                        vm.calcularFatorCorretivoTotal();
                        vm.limparFator();
                    }else{
                        ComumFactory.mostrarMensagemSucesso("Necessário preencher os campos obrigatórios");
                        return;
                    }
                };
                vm.calcularFatorCorretivoTotal = function(){
                    var valor;
                    if(vm.pvgSpu.fatores.length > 0){
                        valor = vm.pvgSpu.fatores[0].valor;
                    }
                    for(var i = 1; i < vm.pvgSpu.fatores.length; i++){
                        valor *= vm.pvgSpu.fatores[i].valor;
                    }
                    if(valor > 2.5){
                        vm.pvgSpu.fatorCorretivoTotal = 2.5;
                        vm.mostrarMsgfator = true;
                    }else if(valor < 0.4){
                        vm.pvgSpu.fatorCorretivoTotal = 0.4;
                        vm.mostrarMsgfator = true;
                    }else{
                        vm.pvgSpu.fatorCorretivoTotal = valor;
                        vm.mostrarMsgfator = false;
                    }
                    vm.calcularValorTerreno();
                };

                vm.editar = function(fator){
                    if (vm.pvgSpu.fatores.length <= 3 && fator.descricao && fator.valor && fator.justificativa) {
                        vm.pvgSpu.fatores.splice(fator.index,1);
                        vm.pvgSpu.fatores[fator.index] = fator;
                        vm.isEditar = false;
                        vm.calcularFatorCorretivoTotal();
                        vm.limparFator();
                    }
                };

                vm.editarFator = function(index,fator){
                    vm.fator = angular.copy(fator);
                    vm.fator.index = index;
                    vm.isEditar = true;
                };


                vm.removerFator = function(index){
                    var confirm = $mdDialog.confirm({focusOnOpen: false})
                        .textContent($filter('translate')('mensagem-remover-fator'))
                        .ok($filter('translate')('btn-sim'))
                        .cancel($filter('translate')('btn-nao'));
                    $mdDialog.show(confirm).then(function () {
                        vm.pvgSpu.fatores.splice(index,1);
                        vm.calcularFatorCorretivoTotal();
                    }, function () {
                        $mdDialog.cancel();
                    });

                };

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                vm.calcularValorTerreno = function(){
                    if(vm.areaTerreno) {
                        vm.pvgSpu.valorTerreno = (vm.pvgSpu.valorAvaliacao * vm.areaTerreno) * (!vm.pvgSpu.fatorCorretivoTotal ? 1.0 : vm.pvgSpu.fatorCorretivoTotal);
                    }else if(vm.pvgSpu.areaFracaoTerreno) {
                        vm.pvgSpu.valorTerreno = (vm.pvgSpu.valorAvaliacao * vm.pvgSpu.areaFracaoTerreno) * (!vm.pvgSpu.fatorCorretivoTotal ? 1.0 : vm.pvgSpu.fatorCorretivoTotal);
                    }
                    if(!vm.pvgSpu.valorTerreno){
                        vm.pvgSpu.valorTerreno = 0;
                    }
                };

                vm.limparDadosArquivos = function () {
                    vm.fator.arquivos = [];
                };

                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cancelarAvaliacaoPvgMunicipioController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "comumAvaliacaoService", "$state", "formularioAvaliacaoService", "$filter", "pvgSpuService", "pvgAvaliacaoService", "ComumFactory", function ($scope, laudoAvaliacaoService, $stateParams, comumAvaliacaoService, $state, formularioAvaliacaoService,
                      $filter, pvgSpuService, pvgAvaliacaoService, ComumFactory) {

                var vm = $scope;
                vm.rip = $stateParams.rip;
                vm.id = $stateParams.idPvg;
                vm.selecioneMotivo = '';
                vm.pvg = {};
                vm.data = new Date();
                vm.justificativa = '';

                var init = function () {

                    vm.titulo = $filter('translate')('title-cancelar-referencia_valor_venal_municipio');
                    vm.dataMostrar = $filter('date')(new Date(), 'dd/MM/yyyy');

                    comumAvaliacaoService.usuarioLogado().then(function (resp) {
                        vm.nomeResponsavel = resp.data;
                    });

                    comumAvaliacaoService.consultarMotivosCancelar()
                        .then(function (resp) {
                            vm.motivosCancelar = resp.data;
                        });
                };

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                vm.cancelar = function (formularioCancelarLaudoRelatorio) {
                    vm.validarFormulario(formularioCancelarLaudoRelatorio);
                    if (!formularioCancelarLaudoRelatorio.$valid) {
                        return;
                    }

                    vm.pvg.dataCancelamento = vm.data;
                    vm.pvg.justificativaCancelamento = vm.justificativa;
                    vm.pvg.motivoCancelamento = vm.selecioneMotivo;
                    vm.pvg.id = vm.id;
                    vm.pvg.ripImovel = vm.rip;
                    vm.pvg.responsavelCancelamento = vm.nomeResponsavel.responsavelCancelamento;

                    pvgAvaliacaoService.cancelarPvg(vm.pvg).then(function () {
                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                        $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                            ripDetalhar: vm.rip
                        });
                    });

                }


                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cadastrarAvaliacaoPvgMunicipioController',
            ["$scope", "comumAvaliacaoService", "$stateParams", "$state", "$timeout", "$filter", "ComumFactory", "pvgAvaliacaoService", "formularioAvaliacaoService", "imovelAvaliacaoService", function ($scope, comumAvaliacaoService, $stateParams, $state, $timeout,
                      $filter, ComumFactory, pvgAvaliacaoService, formularioAvaliacaoService, imovelAvaliacaoService) {

                var vm = $scope;

                vm.detalharPvgMunicipio = false;
                vm.rip = $stateParams.rip;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.pvgMunicipio = {
                    documentos: [],
                    valorTerreno: 0
                };
                var MUNICIPIO = 'MUNICIPIO';

                var init = function () {
                    if ($stateParams.idPvg) {
                        vm.idPvg = $stateParams.idPvg;
                        comumAvaliacaoService.consultarMotivosCancelar().then(function (resp) {
                            vm.motivosCancelar = resp.data;
                            pvgAvaliacaoService.consultarPorId(vm.idPvg).then(function (resp) {
                                vm.pvgMunicipio = resp.data;
                                vm.pvgMunicipio.dataInicial = comumAvaliacaoService.recuperarData(vm.pvgMunicipio.dataInicial);
                                vm.pvgMunicipio.dataFinal = vm.pvgMunicipio.dataFinal ?
                                    comumAvaliacaoService.recuperarData(vm.pvgMunicipio.dataFinal) : undefined;
                                vm.areaTerreno = vm.pvgMunicipio.areaTerreno;
                                vm.pvgMunicipio.motivoCancelamento = vm.motivosCancelar[vm.pvgMunicipio.motivoCancelamento];
                                vm.detalharPvgMunicipio = true;
                            });
                        });
                    } else {
                        imovelAvaliacaoService.consultarImovelPorRip(vm.rip).then(function (resposta) {
                            vm.imovel = resposta.data;
                            vm.pvgMunicipio.ufImovel = vm.imovel.enderecamento.uf;

                            definirAreaTerreno();
                        });
                    }
                };


                vm.verificarDatas = function () {
                    pvgAvaliacaoService.verificarDatas(vm.rip, MUNICIPIO, vm.pvgMunicipio.dataInicial).then(function (resp) {
                        vm.validarDatas = resp.data;
                    });
                };

                vm.changeDataInicial = function () {
                    if (vm.pvgMunicipio.dataInicial > new Date()) {
                        vm.pvgMunicipio.dataInicial = null;
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-inicial-maior-atual'));
                    }
                };

                vm.calcularValorTerreno = function () {
                    var valorCalculado = vm.pvgMunicipio.valor * vm.areaTerreno;
                    vm.pvgMunicipio.valorTerreno = valorCalculado.toFixed(2)
                };


                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                vm.salvarDocumentoPVGMunicipio = function (formularioPVGMunicipio) {
                    vm.validarFormulario(formularioPVGMunicipio);

                    if (!formularioPVGMunicipio.$valid) {
                        return;
                    }

                    vm.verificarDatas();

                    vm.pvgMunicipio.ripImovel = vm.rip;
                    vm.pvgMunicipio.tipo = MUNICIPIO;
                    vm.pvgMunicipio.areaTerreno = vm.areaTerreno;


                    $timeout(function () {
                        if (vm.validarDatas) {
                            vm.dataInicial = $filter('date')(vm.pvgMunicipio.dataInicial, "dd/MM/yyyy");
                            var msg = "Você está cadastrando uma avaliação a partir da data " + vm.dataInicial + ". Esta ação implica no cancelamento de outras avaliações ativas ou vigente da mesma data. Deseja continuar?";

                            if (confirm(msg)) {
                                pvgAvaliacaoService.salvarPvg(vm.pvgMunicipio).then(function () {
                                    ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                        ripDetalhar: vm.rip
                                    });
                                });
                            }
                        } else {
                            pvgAvaliacaoService.salvarPvg(vm.pvgMunicipio).then(function () {
                                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                    ripDetalhar: vm.rip
                                });
                            });
                        }
                    }, 200);
                };

                var definirAreaTerreno = function () {
                    if (vm.imovel.proprietarioTitular) {
                        if ((vm.imovel.proprietarioTitular.idProprietarioReal && vm.imovel.proprietarioTitular.idProprietarioReal != '1')
                            || !vm.imovel.proprietarioTitular.uniaoParcialmente) {
                            /*area total*/
                            if (vm.imovel.terreno.areaTotalCadastral != 0 &&
                                vm.imovel.terreno.areaTotalCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCadastral;
                            }
                        } else if ((vm.imovel.tipoImovel.descricao == 'Unidade autônoma' &&
                            !vm.imovel.proprietarioTitular.uniaoParcialmente) ||
                            ((vm.imovel.proprietarioTitular.idProprietarioReal && vm.imovel.proprietarioTitular.idProprietarioReal != '1'))) {
                            /*Fração Ideal do Imóvel*/
                            if (vm.imovel.terreno.areaTotalCadastral != 0 &&
                                vm.imovel.terreno.areaTotalCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCadastral;
                            }
                        } else if (vm.imovel.proprietarioTitular.uniaoParcialmente &&
                            vm.imovel.tipoImovel.descricao != 'Unidade autônoma') {
                            /*Area da Uniao*/
                            if (vm.imovel.terreno.areaUniaoCadastral != 0 &&
                                vm.imovel.terreno.areaUniaoCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCadastral;
                            }
                        } else if (vm.imovel.proprietarioTitular.uniaoParcialmente &&
                            vm.imovel.tipoImovel.descricao == 'Unidade autônoma') {
                            /*Fração Ideal da União*/
                            if (vm.imovel.terreno.areaUniaoCadastral != 0 &&
                                vm.imovel.terreno.areaUniaoCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCadastral;
                            }
                        }
                    }
                };

                vm.limparDadosDocumentos = function () {
                    vm.pvgMunicipio.documentos = [];
                };

                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cancelarAvaliacaoPvgIncraController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "comumAvaliacaoService", "$state", "formularioAvaliacaoService", "$filter", "pvgSpuService", "pvgAvaliacaoService", "ComumFactory", function ($scope, laudoAvaliacaoService, $stateParams, comumAvaliacaoService, $state, formularioAvaliacaoService,
                      $filter, pvgSpuService, pvgAvaliacaoService, ComumFactory) {

                var vm = $scope;
                vm.rip = $stateParams.rip;
                vm.id = $stateParams.idPvg;
                vm.selecioneMotivo = '';
                vm.pvg = {};
                vm.data = new Date();
                vm.justificativa = '';

                var init = function () {

                    vm.titulo = $filter('translate')('title-cancelar-referencia_incra');
                    vm.dataMostrar = $filter('date')(new Date(), 'dd/MM/yyyy');

                    comumAvaliacaoService.usuarioLogado().then(function (resp) {
                        vm.nomeResponsavel = resp.data;
                    });

                    comumAvaliacaoService.consultarMotivosCancelar()
                        .then(function (resp) {
                            vm.motivosCancelar = resp.data;
                        });
                };

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                vm.cancelar = function (formularioCancelarLaudoRelatorio) {
                    vm.validarFormulario(formularioCancelarLaudoRelatorio);
                    if (!formularioCancelarLaudoRelatorio.$valid) {
                        return;
                    }
                    vm.pvg.dataCancelamento = vm.data;
                    vm.pvg.justificativaCancelamento = vm.justificativa;
                    vm.pvg.motivoCancelamento = vm.selecioneMotivo;
                    vm.pvg.id = vm.id;
                    vm.pvg.ripImovel = vm.rip;
                    vm.pvg.responsavelCancelamento = vm.nomeResponsavel.responsavelCancelamento;

                    pvgAvaliacaoService.cancelarPvg(vm.pvg).then(function () {
                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                        $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                            ripDetalhar: vm.rip
                        });
                    });

                }

                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cadastrarAvaliacaoPvgIncraController',
            ["$scope", "comumAvaliacaoService", "$stateParams", "$state", "$timeout", "$filter", "ComumFactory", "pvgAvaliacaoService", "formularioAvaliacaoService", "imovelAvaliacaoService", function ($scope, comumAvaliacaoService, $stateParams, $state, $timeout,
                      $filter, ComumFactory, pvgAvaliacaoService, formularioAvaliacaoService, imovelAvaliacaoService) {

                var vm = $scope;

                vm.detalharPvgIncra = false;
                vm.rip = $stateParams.rip;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.pvgIncra = {
                    documentos: [],
                    valorTerreno: 0
                };

                var INCRA = 'INCRA';
                vm.M2 = 'M2';
                vm.HA = 'HA';

                vm.formatterTipoUnidade = '';
                vm.listaTipoUnidades = [];

                var init = function () {
                    comumAvaliacaoService.consultarMotivosCancelar().then(function (resp) {
                        vm.motivosCancelar = resp.data;
                        if ($stateParams.idPvg) {
                            vm.idPvg = $stateParams.idPvg;
                            pvgAvaliacaoService.consultarPorId(vm.idPvg).then(function (resp) {
                                vm.pvgIncra = resp.data;

                                vm.pvgIncra.dataInicial = comumAvaliacaoService.recuperarData(vm.pvgIncra.dataInicial);
                                vm.areaTerreno = vm.pvgIncra.areaTerreno;
                                vm.detalharPvgIncra = true;
                                vm.pvgIncra.motivoCancelamento = vm.motivosCancelar[vm.pvgIncra.motivoCancelamento];
                            });
                        } else {
                            imovelAvaliacaoService.consultarImovelPorRip(vm.rip).then(function (resposta) {
                                vm.imovel = resposta.data;
                                vm.pvgIncra.ufImovel = vm.imovel.enderecamento.uf;

                                definirAreaTerreno();
                            });
                        }
                    });

                    comumAvaliacaoService.buscarTiposUnidade().then(function (resp) {
                        vm.listaTipoUnidades = resp.data;
                    });
                };

                vm.formatarTipoUnidadeValor = function () {
                    if (vm.pvgIncra.tipoUnidade === vm.M2) {
                        vm.formatterTipoUnidade = '/m²';
                    } else if (vm.pvgIncra.tipoUnidade === vm.HA) {
                        vm.formatterTipoUnidade = '/ha';
                    }
                    limparValoresTerreno();
                };

                var limparValoresTerreno = function () {
                    vm.pvgIncra.valorTerreno = undefined;
                    vm.pvgIncra.valor = undefined;
                };

                vm.limparDadosDocumentos = function () {
                    vm.pvgIncra.documentos = [];
                };

                vm.calcularValorTerreno = function () {
                    var valorCalculado = null;
                    if (vm.pvgIncra.tipoUnidade === vm.M2) {
                        valorCalculado = vm.pvgIncra.valor * vm.areaTerreno;
                        vm.pvgIncra.valorTerreno = valorCalculado.toFixed(2)
                    } else if (vm.pvgIncra.tipoUnidade === vm.HA) {
                        valorCalculado = vm.areaTerreno * (vm.pvgIncra.valor / 10000);
                        vm.pvgIncra.valorTerreno = valorCalculado.toFixed(2)
                    }
                };


                vm.salvarDocumentoPVGIncra = function (formularioPvgIncra) {
                    vm.validarFormulario(formularioPvgIncra);

                    if (!formularioPvgIncra.$valid) {
                        return;
                    }

                    vm.verificarDatas();

                    vm.pvgIncra.ripImovel = vm.rip;
                    vm.pvgIncra.tipo = INCRA;
                    vm.pvgIncra.areaTerreno = vm.areaTerreno;

                    $timeout(function () {
                        if (vm.validarDatas) {
                            vm.dataInicial = $filter('date')(vm.pvgIncra.dataInicial, "dd/MM/yyyy");
                            var msg = "Você está cadastrando uma avaliação a partir da data " + vm.dataInicial + ". Esta ação implica no cancelamento de outras avaliações ativas ou vigente da mesma data. Deseja continuar?";

                            if (confirm(msg)) {
                                pvgAvaliacaoService.salvarPvg(vm.pvgIncra).then(function () {
                                    ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                        ripDetalhar: vm.rip
                                    });
                                });
                            }
                        } else {
                            pvgAvaliacaoService.salvarPvg(vm.pvgIncra).then(function () {
                                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                    ripDetalhar: vm.rip
                                });
                            });
                        }
                    }, 200)
                };

                vm.verificarDatas = function () {
                    pvgAvaliacaoService.verificarDatas(vm.rip, INCRA, vm.pvgIncra.dataInicial).then(function (resp) {
                        vm.validarDatas = resp.data;
                    });
                };

                vm.changeDataInicial = function () {
                    if (vm.pvgIncra.dataInicial > new Date()) {
                        vm.pvgIncra.dataInicial = null;
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-inicial-maior-atual'));
                    }
                };

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                var definirAreaTerreno = function () {
                    if (vm.imovel.proprietarioTitular) {
                        if ((vm.imovel.proprietarioTitular.idProprietarioReal && vm.imovel.proprietarioTitular.idProprietarioReal != '1')
                            || !vm.imovel.proprietarioTitular.uniaoParcialmente) {
                            /*area total*/
                            if (vm.imovel.terreno.areaTotalCadastral != 0 &&
                                vm.imovel.terreno.areaTotalCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCadastral;
                            }
                        } else if ((vm.imovel.tipoImovel.descricao == 'Unidade autônoma' &&
                            !vm.imovel.proprietarioTitular.uniaoParcialmente) ||
                            ((vm.imovel.proprietarioTitular.idProprietarioReal && vm.imovel.proprietarioTitular.idProprietarioReal != '1'))) {
                            /*Fração Ideal do Imóvel*/
                            if (vm.imovel.terreno.areaTotalCadastral != 0 &&
                                vm.imovel.terreno.areaTotalCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaTotalCadastral;
                            }
                        } else if (vm.imovel.proprietarioTitular.uniaoParcialmente &&
                            vm.imovel.tipoImovel.descricao != 'Unidade autônoma') {
                            /*Area da Uniao*/
                            if (vm.imovel.terreno.areaUniaoCadastral != 0 &&
                                vm.imovel.terreno.areaUniaoCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCadastral;
                            }
                        } else if (vm.imovel.proprietarioTitular.uniaoParcialmente &&
                            vm.imovel.tipoImovel.descricao == 'Unidade autônoma') {
                            /*Fração Ideal da União*/
                            if (vm.imovel.terreno.areaUniaoCadastral != 0 &&
                                vm.imovel.terreno.areaUniaoCartorial != 0) {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCartorial;
                            } else {
                                vm.areaTerreno = vm.imovel.terreno.areaUniaoCadastral;
                            }
                        }
                    }
                };

                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('visualizarNotaTecnicaHomologacaoLaudoAvaliacaoController',
            ["$scope", "$stateParams", "laudoAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "imagemAvaliacaoService", "$sce", "$state", function($scope, $stateParams, laudoAvaliacaoService, avaliacaoEscopoCompartilhadoService, imagemAvaliacaoService, $sce, $state) {

                var vm = $scope;

                vm.notaTecnicaHomologacao = {};
                vm.notaTecnicaHomologacao.responsabilidadeTecnica = 'Independentemente da homologação, o valor atribuído é de inteira responsabilidade do avaliador responsável pela avaliação';


                var init = function () {
                    vm.rip = avaliacaoEscopoCompartilhadoService.getObjeto('rip');
                    vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                    vm.laudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao');
                    vm.listaBenfeitoriasImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriasImovel');
                    vm.unidadeAutonomaImovel = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaImovel');
                    vm.tipoValorLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('tipoValorLaudo');
                    vm.detalharLaudoRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('detalharLaudoRelatorio');
                    vm.acaoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('acaoAvaliacao');
                    vm.notaTecnicaHomologacao = angular.copy(vm.laudoAvaliacao.notaTecnicaHomologacao);
                    angular.forEach(vm.notaTecnicaHomologacao.anexos, function(anexo) {
                        anexo.sce = $sce.trustAsResourceUrl(vm.gerarImagem(anexo.arquivo.binario, anexo.arquivo.contentType));
                    });
                    if (!vm.rip && !vm.laudoAvaliacao) {
                        window.history.back();
                    }
                };

                vm.fecharInserirNotaTecnica = function () {
                    preencherRipLaudoEscopoCompartilhado();
                    $state.go('avaliacao.INSERIR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO');
                };

                vm.voltarDetalharLaudo = function () {
                    if (vm.laudoAvaliacao.tipoAvaliacao === 'LAUDO_AVALIACAO') {
                        $state.go('avaliacao.DETALHAR_LAUDO_AVALIACAO', {
                            'rip': vm.rip,
                            'idLaudo': vm.laudoAvaliacao.id
                        });
                    }else{
                        $state.go('avaliacao.DETALHAR_LAUDO_AVALIACAO_LOCACAO', {
                            'rip': vm.rip,
                            'idLaudo': vm.laudoAvaliacao.id
                        });
                    }
                };

                var preencherRipLaudoEscopoCompartilhado = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriasImovel', vm.listaBenfeitoriasImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaImovel', vm.laudoAvaliacao.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoValorLaudo', vm.laudoAvaliacao.tipoValorLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('notaTecnicaHomologacao', vm.notaTecnicaHomologacao);
                };

                vm.usuarioLogadoHomologador = function(homologadores){
                    for(var i = 0; i < homologadores.length; i++){
                        if(homologadores[i].cpf === vm.usuarioLogado.cpf){
                            return true;
                        }
                    }
                    return false;
                };

                vm.gerarImagem = function(base64, contentType) {
                    return imagemAvaliacaoService.gerarPreviewImagem(base64, contentType);
                };

                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('inserirNotaTecnicaHomologacaoLaudoAvaliacaoController',
            ["$scope", "avaliacaoEscopoCompartilhadoService", "formularioAvaliacaoService", "$state", function ($scope, avaliacaoEscopoCompartilhadoService, formularioAvaliacaoService, $state) {

                var vm = $scope;

                vm.notaTecnicaHomologacao = {};
                vm.notaTecnicaHomologacao.responsabilidadeTecnica = 'Independentemente da homologação, o valor atribuído é de inteira responsabilidade do avaliador responsável pela avaliação.';

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                var init = function () {
                    vm.rip = avaliacaoEscopoCompartilhadoService.getObjeto('rip');
                    vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                    vm.laudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao');
                    vm.listaBenfeitoriasImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriasImovel');
                    vm.unidadeAutonomaImovel = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaImovel');
                    vm.tipoValorLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('tipoValorLaudo');
                    if (avaliacaoEscopoCompartilhadoService.getObjeto('notaTecnicaHomologacao')) {
                        vm.notaTecnicaHomologacao = avaliacaoEscopoCompartilhadoService.getObjeto('notaTecnicaHomologacao');
                    }
                    if (vm.laudoAvaliacao.notaTecnicaHomologacao) {
                        vm.notaTecnicaHomologacao = angular.copy(vm.laudoAvaliacao.notaTecnicaHomologacao);
                    }

                    avaliacaoEscopoCompartilhadoService.limparEscopo();


                    if (!vm.rip && !vm.laudoAvaliacao) {
                        window.history.back();
                    }
                };

                vm.fecharInserirNotaTecnica = function () {
                    recuperarDadosLaudoAvaliacao();
                    if (vm.laudoAvaliacao.tipoAvaliacao === 'LAUDO_AVALIACAO') {
                        if (vm.laudoAvaliacao.id) {
                            $state.go('avaliacao.EDITAR_LAUDO_AVALIACAO', {
                                'rip': vm.rip,
                                'idLaudo': vm.laudoAvaliacao.id
                            });
                        } else {
                            $state.go('avaliacao.CADASTRAR_LAUDO_AVALIACAO', {
                                rip: vm.rip
                            });
                        }
                    } else {
                        if (vm.laudoAvaliacao.id) {
                            $state.go('avaliacao.EDITAR_LAUDO_AVALIACAO_LOCACAO', {
                                'rip': vm.rip,
                                'idLaudo': vm.laudoAvaliacao.id
                            });
                        } else {
                            $state.go('avaliacao.CADASTRAR_LAUDO_AVALIACAO_LOCACAO', {
                                rip: vm.rip
                            });
                        }
                    }
                };

                vm.concluirPreenchimentoNotaTecnica = function (formularioDados) {
                    // vm.validarFormulario(formularioDados);

                    if (!formularioDados.$valid) {
                        vm.notaTecnicaHomologacao.dataPreenchimento = null;
                        vm.laudoAvaliacao.notaTecnicaHomologacao = vm.notaTecnicaHomologacao;
                        recuperarDadosLaudoAvaliacao();
                        vm.fecharInserirNotaTecnica();
                        return
                    }
                    vm.notaTecnicaHomologacao.dataPreenchimento = new Date();
                    vm.laudoAvaliacao.notaTecnicaHomologacao = vm.notaTecnicaHomologacao;
                    recuperarDadosLaudoAvaliacao();
                    vm.fecharInserirNotaTecnica();
                };

                vm.visualizarNotaTecnicaHomologacao = function () {

                    vm.notaTecnicaHomologacao.dataPreenchimento = new Date();
                    vm.laudoAvaliacao.notaTecnicaHomologacao = vm.notaTecnicaHomologacao;
                    recuperarDadosLaudoAvaliacao();

                    $state.go('avaliacao.VISUALIZAR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO');
                };

                var recuperarDadosLaudoAvaliacao = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriasImovel', vm.listaBenfeitoriasImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaImovel', vm.unidadeAutonomaImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoValorLaudo', vm.tipoValorLaudo);
                };

                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('assinarNotaTecnicaHomologacaoLaudoAvaliacaoController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "$state", "ComumFactory", "$filter", "usuarioAvaliacaoService", "imagemAvaliacaoService", "$sce", "$mdDialog", function($scope, laudoAvaliacaoService, $stateParams, $state, ComumFactory, $filter, usuarioAvaliacaoService, imagemAvaliacaoService, $sce, $mdDialog   ) {

                var vm = $scope;

                vm.laudoAvaliacao = {};
                vm.notaTecnicaHomologacao = {};

                var init = function () {
                    vm.idLaudo = $stateParams.idLaudo;

                    laudoAvaliacaoService.consultarLaudoAvaliacaoId(vm.idLaudo).then(function(resp) {
                        vm.laudoAvaliacao = resp.data;
                        vm.notaTecnicaHomologacao = angular.copy(vm.laudoAvaliacao.notaTecnicaHomologacao);
                        angular.forEach(vm.notaTecnicaHomologacao.anexos, function(anexo) {
                            anexo.sce = $sce.trustAsResourceUrl(vm.gerarImagem(anexo.arquivo.binario, anexo.arquivo.contentType));
                        });
                        buscarUsuarioLogado().then(function(resp){
                            vm.usuarioLogado = resp.data;
                            if(!vm.usuarioLogadoHomologador(vm.laudoAvaliacao.homologadores)){
                                window.history.back();
                            }
                        });

                    });
                };

                vm.usuarioLogadoHomologador = function(homologadores){
                    for(var i = 0; i < homologadores.length; i++){
                        if(homologadores[i].cpf === vm.usuarioLogado.cpf){
                            return true;
                        }
                    }
                    return false;
                };

                var buscarUsuarioLogado = function() {
                    return usuarioAvaliacaoService.getUsuarioLogado();
                };

                vm.redirecionarDetalharImovelAvaliacao = function() {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.laudoAvaliacao.ripImovel
                    });
                };

                vm.recusarNotaTecnicaHomologacaoLaudoAvaliacao = function() {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Tem certeza que deseja recusar esta Nota Técnica de Homologação?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        laudoAvaliacaoService.recusarLaudoAvaliacaoNotaTecnica(vm.idLaudo).then(function() {
                            $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                ripDetalhar: vm.laudoAvaliacao.ripImovel
                            });
                        });
                    }, function () {

                    });

                };

                vm.assinarLaudoRelatorio = function() {
                    laudoAvaliacaoService.assinarLaudoAvaliacaoNotaTecnica(vm.idLaudo).then(function() {
                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-assinatura-registrada-sucesso'));
                        $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                            ripDetalhar: vm.laudoAvaliacao.ripImovel
                        });
                    });
                };

                vm.gerarImagem = function(base64, contentType) {
                    return imagemAvaliacaoService.gerarPreviewImagem(base64, contentType);
                };

                init();
            }])

})();

(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory("unidadeAutonomaAvaliacaoService", ["$http", function($http){

            var URL_UNIDADE_AUTONOMA = '/avaliacao/api/unidade-autonoma';

            var buscarImagensUnidadeAutonoma = function(idUnidadeAutonoma) {
                return $http.get(URL_UNIDADE_AUTONOMA + '/fotos/' + idUnidadeAutonoma);
            };

            var buscarTiposEdificacao = function() {
                return $http.get(URL_UNIDADE_AUTONOMA + '/tipo/edificacao/all');
            };

            var buscarTiposProjeto = function() {
                return $http.get(URL_UNIDADE_AUTONOMA + '/tipo/projeto/all');
            };

            return {
                buscarTiposEdificacao: buscarTiposEdificacao,
                buscarTiposProjeto: buscarTiposProjeto,
                buscarImagensUnidadeAutonoma: buscarImagensUnidadeAutonoma
            }
        }])
})();
(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory('laudoRelatorioService', ["$http", function ($http) {

            var URL_LAUDO_RELATORIO = '/avaliacao/api/laudo-relatorio';

            var buscarSituacoesLaudo = function () {
                return $http.get(URL_LAUDO_RELATORIO + '/situacao/laudo/all');
            };

            var buscarExecucoesVistoria = function () {
                return $http.get(URL_LAUDO_RELATORIO + '/execucao-vistoria/all');
            };

            var consultaLaudosRelatorioPaginacao = function (filtro, offset, limit) {
                return $http.get(URL_LAUDO_RELATORIO, {
                        params: {
                            tipo: filtro.tipo,
                            metodoAvaliacao: filtro.metodoAvaliacao,
                            situacaoLaudo: filtro.situacaoLaudo,
                            dataInicialAssinatura: filtro.dataInicialAssinatura,
                            dataFinalAssinatura: filtro.dataFinalAssinatura,
                            numeroLaudo: filtro.numeroLaudo,
                            validade: filtro.validade,
                            rip: filtro.rip,
                            idTipoImovel: filtro.idTipoImovel,
                            idNaturezaImovel: filtro.idNaturezaImovel,
                            pais: filtro.pais,
                            uf: filtro.uf,
                            cep: filtro.cep,
                            municipio: filtro.municipio,
                            cidadeExterior: filtro.cidadeExterior,
                            bairro: filtro.bairro,
                            logradouro: filtro.logradouro,
                            tipoProprietario: filtro.tipoProprietario,
                            campoResponsavelTecnico: filtro.campoResponsavelTecnico,
                            offset: offset,
                            limit: limit
                        }
                    }
                );
            };

            var buscarTiposImovel = function () {
                return $http.get(URL_LAUDO_RELATORIO + '/tipo/imovel/all');
            };

            var buscarNaturezaImovel = function () {
                return $http.get(URL_LAUDO_RELATORIO + '/natureza/imovel/all');
            };

            var buscarTiposUnidadeAutonoma = function () {
                return $http.get(URL_LAUDO_RELATORIO + '/tipo/unidade/autonoma/all');
            };

            var salvarRascunho = function (laudoRelatorio) {
                return $http.post(URL_LAUDO_RELATORIO + '/rascunho', laudoRelatorio);
            };

            var concluirCadastro = function (laudoRelatorio) {
                return $http.post(URL_LAUDO_RELATORIO + '/concluir', laudoRelatorio);
            };

            var consultarPorId = function (id) {
                return $http.get(URL_LAUDO_RELATORIO + '/consultar/' + id);
            };

            var assinarLaudoRelatorio = function (id) {
                return $http.put(URL_LAUDO_RELATORIO + '/assinar/' + id);
            };

            var recuperarDocumentosCartoriais = function (idDetalhesImovel) {
                return $http.get(URL_LAUDO_RELATORIO + '/documentos/cartoriais/' + idDetalhesImovel);
            };

            var recuperarDocumentosParametrosUrbanisticos = function (idTerrenoImovel) {
                return $http.get(URL_LAUDO_RELATORIO + '/documentos/parametros/urbanisticos/' + idTerrenoImovel);
            };

            var buscarLaudosRelatorioAguardandoVinculacao = function (rip, offset, limit) {
                return $http.get(URL_LAUDO_RELATORIO + '/vinculo/laudo-avaliacao', {
                    params: {
                        rip: rip,
                        offset: offset,
                        limit: limit
                    }
                });
            };

            var deletarLaudoRelatorio = function (id) {
                return $http.delete(URL_LAUDO_RELATORIO + '/' + id);
            };

            var downloadLaudoRelatorioAvaliacao = function (id) {
                return URL_LAUDO_RELATORIO + '/relatorio/' + id;
            };

            var salvarImagemLocalizacao = function (imovel) {
                return $http.post(URL_LAUDO_RELATORIO + '/salvar-imagem-localizacao', imovel);
            };

            var salvarImagemMapaAmostras = function (imovel) {
                return $http.post(URL_LAUDO_RELATORIO + '/salvar-imagem-mapa-amostras', imovel);
            };

            return {
                salvarRascunho: salvarRascunho,
                buscarSituacoesLaudo: buscarSituacoesLaudo,
                buscarExecucoesVistoria: buscarExecucoesVistoria,
                consultaLaudosRelatorioPaginacao: consultaLaudosRelatorioPaginacao,
                buscarTiposImovel: buscarTiposImovel,
                buscarNaturezaImovel: buscarNaturezaImovel,
                buscarTiposUnidadeAutonoma: buscarTiposUnidadeAutonoma,
                concluirCadastro: concluirCadastro,
                consultarPorId: consultarPorId,
                assinarLaudoRelatorio: assinarLaudoRelatorio,
                recuperarDocumentosCartoriais: recuperarDocumentosCartoriais,
                recuperarDocumentosParametrosUrbanisticos: recuperarDocumentosParametrosUrbanisticos,
                buscarLaudosRelatorioAguardandoVinculacao: buscarLaudosRelatorioAguardandoVinculacao,
                deletarLaudoRelatorio: deletarLaudoRelatorio,
                downloadLaudoRelatorioAvaliacao: downloadLaudoRelatorioAvaliacao,
                salvarImagemLocalizacao: salvarImagemLocalizacao,
                salvarImagemMapaAmostras: salvarImagemMapaAmostras
            };
        }]);
})();
(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory("identificacaoLaudoRelatorioService", ["$http", function ($http) {

            var URL_IDENTIFICACAO = '/avaliacao/api/identificacao';

            var buscarTiposIdentificacao = function () {
                return $http.get(URL_IDENTIFICACAO + '/tipos/all');
            };

            var buscarMetodosAvaliacao = function () {
                return $http.get(URL_IDENTIFICACAO + '/metodos/avaliacao/all');
            };

            var buscarTratamentosIdentificacao = function () {
                return $http.get(URL_IDENTIFICACAO + '/tratamentos/all');
            };

            var buscarMetodologia = function () {
                return $http.get(URL_IDENTIFICACAO + '/metodologia/all');
            };

            var buscarFerramentasAnaliticas = function () {
                return $http.get(URL_IDENTIFICACAO + '/ferramentas/analiticas/all');
            };

            var buscarTiposNorma = function () {
                return $http.get(URL_IDENTIFICACAO + '/tipo-norma/all');
            };

            var buscarQuantificacaoCustoBenfeitorias = function () {
                return $http.get(URL_IDENTIFICACAO + '/quantificacao-custo-benfeitoria/all');
            };

            var buscarTiposAvaliacaoTerreno = function () {
                return $http.get(URL_IDENTIFICACAO + '/tipo-avaliacao-terreno/all');
            };

            var verificarAvaliacoesExistentes = function (rip, tipo) {
                return $http.get(URL_IDENTIFICACAO + '/verificar/avaliacoes-existentes/' + rip + "/" + tipo);
            };

            var buscarCUB = function () {
                return $http.get(URL_IDENTIFICACAO + '/cub/all');
            };

            return {
                buscarTiposIdentificacao: buscarTiposIdentificacao,
                buscarMetodosAvaliacao: buscarMetodosAvaliacao,
                buscarTratamentosIdentificacao: buscarTratamentosIdentificacao,
                buscarFerramentasAnaliticas: buscarFerramentasAnaliticas,
                buscarTiposNorma: buscarTiposNorma,
                buscarMetodologia: buscarMetodologia,
                verificarAvaliacoesExistentes: verificarAvaliacoesExistentes,
                buscarQuantificacaoCustoBenfeitorias: buscarQuantificacaoCustoBenfeitorias,
                buscarTiposAvaliacaoTerreno: buscarTiposAvaliacaoTerreno,
                buscarCUB: buscarCUB
            }
        }])
})();

(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory("benfeitoriasLaudoRelatorioService", ["$http", function($http){

            var URL_BENFEITORIAS = '/avaliacao/api/benfeitorias';

            var buscarTiposBenfeitoria = function() {
                return $http.get(URL_BENFEITORIAS + '/tipo/benfeitoria/all');
            };

            var buscarTiposProjeto = function() {
                return $http.get(URL_BENFEITORIAS + '/tipo/projeto/all');
            };

            var buscarTiposEspecializacaoComplementar = function() {
                return $http.get(URL_BENFEITORIAS + '/tipo/complementar/all');
            };

            var buscarTiposEspecializacaoEdificacao = function() {
                return $http.get(URL_BENFEITORIAS + '/tipo/edificacao/all');
            };

            var buscarFotosBenfeitorias = function(idsBenfeitorias) {
                return $http.get(URL_BENFEITORIAS + '/fotos/' + idsBenfeitorias);
            };

            return {
                buscarTiposBenfeitoria: buscarTiposBenfeitoria,
                buscarTiposEspecializacaoComplementar: buscarTiposEspecializacaoComplementar,
                buscarTiposEspecializacaoEdificacao: buscarTiposEspecializacaoEdificacao,
                buscarFotosBenfeitorias: buscarFotosBenfeitorias,
                buscarTiposProjeto: buscarTiposProjeto
            }
        }])
})();
(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory("avaliacaoLaudoService", ["$http", "Upload", function ($http, Upload) {

            var URL_AVALIACAO = '/avaliacao/api/avaliacao';

            var buscarTiposValor = function () {
                return $http.get(URL_AVALIACAO + '/tipos-valor/all');
            };

            var buscarTiposVariavel = function () {
                return $http.get(URL_AVALIACAO + '/tipos-variavel/all');
            };

            var buscarVariaveisEnum = function () {
                return $http.get(URL_AVALIACAO + '/variaveis/all');
            };

            var ordenacaoTipoValor = function () {
                return $http.get(URL_AVALIACAO + '/tipos-valor/sort');
            };

            var buscarNivelFundamentacao = function () {
                return $http.get(URL_AVALIACAO + '/nivel-fundamentacao/all');
            };

            var buscarModelosAvaliacaoGlebaUrbanizavel = function () {
                return $http.get(URL_AVALIACAO + '/modelos-avaliacao-gleba-urbanizavel/all');
            };

            var buscarValorTerreno = function (rip, tipo) {
                return $http.get(URL_AVALIACAO + '/buscar/valor-terreno/' + rip + '/' + tipo);
            };

            var recuperarAnexosAvaliacao = function (idAvaliacao) {
                return $http.get(URL_AVALIACAO + '/anexos/avaliacao/' + idAvaliacao);
            };

            var recuperarRelatoriosEstatisticos = function (idResultadosAvaliacao) {
                return $http.get(URL_AVALIACAO + '/buscar/relatorios-estatisticos/' + idResultadosAvaliacao);
            };

            var buscarDocumentosPressupostos = function (idAvaliacao) {
                return $http.get(URL_AVALIACAO + '/buscar/documentos-pressupostos/' + idAvaliacao);
            };

            var salvarVariavel = function (variavel) {
                return $http.post(URL_AVALIACAO + '/variavel/salvar', variavel);
            };

            var excluirVariavel = function (idVariavel) {
                return $http.delete(URL_AVALIACAO + '/variavel/excluir/' + idVariavel);
            };

            var excluirVariavelAmostra = function (idVariavel) {
                return $http.delete(URL_AVALIACAO + '/variavelAmostra/excluir/' + idVariavel);
            };

            var buscarAmostras = function (idAvaliacao) {
                return $http.get(URL_AVALIACAO + '/amostras/' + idAvaliacao);
            };

            var buscarPressupostos = function () {
                return $http.get(URL_AVALIACAO + '/pressupostos/all');
            };

            var getMoedas = function () {
                return $http.get(URL_AVALIACAO + '/moedas/all');
            };

            var gerarTabelaVariaveis = function (variaveis) {
                return $http.get(URL_AVALIACAO + '/gerar-tabela-variaveis?variaveis='+variaveis);
            };

            var importarAmostra = function($file, idAvaliacao){
                return Upload.upload({
                    url: URL_AVALIACAO + '/importar-amostra/' + idAvaliacao,
                    data: {
                        file: $file
                    }
                });
            };

            return {
                buscarTiposValor: buscarTiposValor,
                buscarTiposVariavel: buscarTiposVariavel,
                buscarVariaveisEnum: buscarVariaveisEnum,
                buscarValorTerreno: buscarValorTerreno,
                ordenacaoTipoValor: ordenacaoTipoValor,
                buscarNivelFundamentacao: buscarNivelFundamentacao,
                recuperarAnexosAvaliacao: recuperarAnexosAvaliacao,
                recuperarRelatoriosEstatisticos: recuperarRelatoriosEstatisticos,
                buscarDocumentosPressupostos: buscarDocumentosPressupostos,
                salvarVariavel: salvarVariavel,
                excluirVariavel: excluirVariavel,
                buscarAmostras: buscarAmostras,
                buscarPressupostos: buscarPressupostos,
                getMoedas:getMoedas,
                importarAmostra:importarAmostra,
                gerarTabelaVariaveis:gerarTabelaVariaveis,
                excluirVariavelAmostra:excluirVariavelAmostra,
                buscarModelosAvaliacaoGlebaUrbanizavel:buscarModelosAvaliacaoGlebaUrbanizavel
            }
        }])
})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('visualizarLaudoRelatorioPdfController',
            ["$scope", "ComumFactory", "$filter", "laudoRelatorioService", "$state", "$stateParams", "numeroExtensoAvaliacaoService", "$timeout", "dominioAvaliacaoService", "identificacaoLaudoRelatorioService", "imagemAvaliacaoService", "avaliacaoLaudoService", "$sce", "imovelAvaliacaoService", "comumAvaliacaoService", "cubAvaliacaoService", "contadorRomanoService", "ciMapaAvaliacaoLayersFactory", "ciMapaAvaliacaoViewFactory", "NotificadorService", "MAPA_CONSTANTS", function ($scope, ComumFactory, $filter, laudoRelatorioService, $state, $stateParams, numeroExtensoAvaliacaoService, $timeout,
                      dominioAvaliacaoService, identificacaoLaudoRelatorioService, imagemAvaliacaoService, avaliacaoLaudoService,
                      $sce, imovelAvaliacaoService, comumAvaliacaoService, cubAvaliacaoService, contadorRomanoService, ciMapaAvaliacaoLayersFactory, ciMapaAvaliacaoViewFactory, NotificadorService, MAPA_CONSTANTS) {

                var vm = $scope;

                vm.laudoRelatorio = {};
                vm.amostras = [];
                vm.valorAdotado = 0;

                vm.dataAtual = new Date();

                vm.listaImagensImovel = [];
                vm.listaImagensBenfeitorias = [];
                vm.listaimagensUnidadeAutonoma = [];
                vm.ferramentasAnaliticas = [];
                vm.listaDocumentosCartoriais = [];
                vm.listaDocumentosParametrosUrbanisticos = [];
                vm.dadosProprietario = {};
                vm.linearidade = [];
                vm.normalidade = [];
                vm.homocedasticidade = [];
                vm.verificacaoAutocorrelacao = [];
                vm.colinearidadeMulticolinearidade = [];
                vm.pontosInfluenciantes = [];

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];
                vm.listaMotivosEstimativa = [];

                vm.BRASIL = 'Brasil';
                vm.isAssinar = true;
                vm.carregarMapa = false;
                vm.carregarMapaAmostras = false;

                var TRES = 'TRES';
                var DOIS = 'DOIS';
                var UM = 'UM';
                vm.NAO_ATINGIDO = 'NAO_ATINGIDO';

                vm.isMotivosEstimativa = false;
                vm.listaTerrenosParcela = [];
                ciMapaAvaliacaoLayersFactory.init();
                ciMapaAvaliacaoViewFactory.init();
                var VALOR_MEDIO = 'VALOR_MEDIO';
                var VALOR_MINIMO = 'VALOR_MINIMO';
                var VALOR_MAXIMO = 'VALOR_MAXIMO';
                var OUTRO_VALOR = 'OUTRO_VALOR';
                var HECTARE = 'HECTARE';
                vm.IMAGE_PNG = 'image/png';
                vm.APPLICATION_PDF = 'application/pdf';

                vm.enquadramentoGrau1 = false;
                vm.enquadramentoGrau2 = false;
                vm.enquadramentoGrau3 = false;
                vm.itemEstimativaValorTerrenoGrau3 = false;
                vm.itemEstimativaValorTerrenoGrau2 = false;
                vm.itemEstimativaValorTerrenoGrau1 = false;
                vm.itemEstimativaCustosReedicaoGrau3 = false;
                vm.itemEstimativaCustosReedicaoGrau2 = false;
                vm.itemEstimativaCustosReedicaoGrau1 = false;
                vm.itemFatorComercializacaoGrau3 = false;
                vm.itemFatorComercializacaoGrau2 = false;
                vm.itemFatorComercializacaoGrau1 = false;

                vm.colorTableColumnAvaliacao = {
                    'background-color': 'lightgreen'
                };

                vm.listaTiposDbi = {
                    CALCULADO: "Calculado",
                    JUSTIFICADO: "Justificado",
                    ARBITRADO: "Arbitrado"
                };

                vm.listaTiposDepreciacao = {
                    CUSTO_RECUPERACAO: "Custo de Recuperação",
                    ROSS_HEIDECKE: "Ross Heidecke",
                    ARBITRADO: "Arbitrado"
                };

                vm.listaTiposArea = {
                    AREA_REAL: "Área Real",
                    AREA_EQUIVALENTE_CONSTRUCAO: "Área Equivalente de Construção"
                };

                vm.listaTiposEstimativaCusto = {
                    UTILIZAR_ORCAMENTOS: "Utilizar Orçamentos",
                    APLICAR_PERCENTUAL: "Aplicar Percentual"
                };

                vm.listaTiposFatorComercializacao = {
                    INFERIDO_MERCADO_SEMELHANTE: "Inferido em mercado Semelhante",
                    JUSTIFICADO: "Justificado",
                    ARBITRADO: "Arbitrado"
                };

                vm.tipoFonteValorBnfeitoria = {
                    CUB: "CUB",
                    OUTRA_FONTE: "Outra Fonte"
                };

                vm.listaValorUnitario = {
                    INFERENCIA_ESTATISTICA: "Inferência Estatística",
                    DOMINIO_PLENO: "Domínio Pleno",
                    OUTRA_METODOLOGIA: "Outra Metodologia"
                };

                var init = function () {
                    contadorRomanoService.reset();
                    vm.idLaudo = $stateParams.idLaudo;
                    buscarMetodosAvaliacao();
                    buscarMetodologia();
                    buscarTratamentosIdentificacao();
                    buscarFerramentasAnaliticas();
                    buscarQuantificacaoCustoBenfeitorias();
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                    pesquisarMotivosEstimativaValor();
                    pesquisarVariaveisEnum();
                    pesquisarTiposVariaveis();
                    buscarExecucoesVistoria();

                    laudoRelatorioService.consultarPorId(vm.idLaudo).then(function (resp) {
                        vm.laudoRelatorio = resp.data;
                        vm.imovel = vm.laudoRelatorio.detalhesImovel.imovel;
                        buscarPonto(vm.imovel.rip);
                        validarExisteMotivoEstimativa();
                        buscarAmostras();

                        for (var i = 0; i < vm.laudoRelatorio.avaliacao.documentosPressupostos.length; i++) {
                            if (vm.laudoRelatorio.avaliacao.documentosPressupostos[i].pressuposto === 'LINEARIDADE') {
                                vm.linearidade.push(vm.laudoRelatorio.avaliacao.documentosPressupostos[i]);
                            } else if (vm.laudoRelatorio.avaliacao.documentosPressupostos[i].pressuposto === 'NORMALIDADE') {
                                vm.normalidade.push(vm.laudoRelatorio.avaliacao.documentosPressupostos[i]);
                            } else if (vm.laudoRelatorio.avaliacao.documentosPressupostos[i].pressuposto === 'HOMOCEDASTICIDADE') {
                                vm.homocedasticidade.push(vm.laudoRelatorio.avaliacao.documentosPressupostos[i]);
                            } else if (vm.laudoRelatorio.avaliacao.documentosPressupostos[i].pressuposto === 'VERIFICACAO_DE_AUTOCORRELACAO') {
                                vm.verificacaoAutocorrelacao.push(vm.laudoRelatorio.avaliacao.documentosPressupostos[i]);
                            } else if (vm.laudoRelatorio.avaliacao.documentosPressupostos[i].pressuposto === 'COLINEARIDADE_OU_MULTICOLINEARIDADE') {
                                vm.colinearidadeMulticolinearidade.push(vm.laudoRelatorio.avaliacao.documentosPressupostos[i]);
                            } else if (vm.laudoRelatorio.avaliacao.documentosPressupostos[i].pressuposto === 'PONTOS_INFLUENCIANTES_OU_OUTLIERS') {
                                vm.pontosInfluenciantes.push(vm.laudoRelatorio.avaliacao.documentosPressupostos[i]);
                            }
                        }

                        buscarImagensImovel(vm.laudoRelatorio.detalhesImovel.imovel.id);
                        buscarImagensBenfeitorias(vm.laudoRelatorio.benfeitorias.id);
                        if (vm.laudoRelatorio.unidadeAutonoma) {
                            buscarImagensUnidadeAutonoma(vm.laudoRelatorio.unidadeAutonoma.id);
                        }
                        buscarDocumentosCartoriais(vm.laudoRelatorio.detalhesImovel.id);
                        buscarDocumentosParametrosUrbanisticos(vm.laudoRelatorio.terrenoImovel.id);
                        buscarAnexosAvaliacao(vm.laudoRelatorio.avaliacao.id);
                        buscarDadosProprietarioImovel(vm.laudoRelatorio.detalhesImovel.imovel.idOriginal);
                        buscarValorTerreno();

                        if (vm.laudoRelatorio.avaliacao.resultadosAvaliacao) {
                            vm.valorAdotado = validarValorLaudoRelatorio(vm.laudoRelatorio.avaliacao.resultadosAvaliacao.tipoValor, vm.laudoRelatorio.avaliacao.resultadosAvaliacao);
                        }

                        if (vm.laudoRelatorio.identificacao.metodologia === 'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA' || vm.laudoRelatorio.identificacao.metodoAvaliacao === 'EVOLUTIVO') {
                            calcularGrauMetodoEvolutivo();
                            calcularValorTotalCustoBenfeitoria();
                            tipoValorTerrenoAvaliacao();
                            vm.calcularGrauFundamentacaoMetodoEvolutivo();
                        }

                        vm.unidadeMedida = vm.laudoRelatorio.terrenoImovel.terreno.unidadeMedida === HECTARE ? 'ha' : 'm²';

                        angular.forEach(vm.laudoRelatorio.anexo.anexos, function (anexo) {
                            angular.forEach(anexo.filhos, function (filho) {
                                if (filho.arquivo) {
                                    filho.sce = $sce.trustAsResourceUrl(vm.gerarImagem(filho.arquivo.binario, filho.arquivo.contentType));
                                }
                            });
                        });
                    });
                };

                var validarExisteMotivoEstimativa = function () {
                    if (vm.laudoRelatorio.benfeitorias.benfeitoriasAvaliacao && vm.laudoRelatorio.benfeitorias.benfeitoriasAvaliacao.length > 0) {
                        angular.forEach(vm.laudoRelatorio.benfeitorias.benfeitoriasAvaliacao, function (benfeitoriaAvaliacao) {
                            vm.listaTerrenosParcela.push({
                                id: benfeitoriaAvaliacao.benfeitoriaImovel.codigo,
                                tipo: benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.descricao,
                                descricao: ((benfeitoriaAvaliacao.benfeitoriaImovel.edificacao && benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao) ? benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoriaAvaliacao.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao),
                                areaConstruida: benfeitoriaAvaliacao.benfeitoriaImovel.areaConstruida,
                                vidaUtil: benfeitoriaAvaliacao.benfeitoriaImovel.vidaUtil,
                                idadeAparente: benfeitoriaAvaliacao.benfeitoriaImovel.idadeAparente,
                                padraoAcabamento: vm.listaPadraoAcabamento[benfeitoriaAvaliacao.benfeitoriaImovel.padraoAcabamento],
                                tipoProjeto: vm.listaTiposProjeto[benfeitoriaAvaliacao.benfeitoriaImovel.tipoProjetoCub],
                                estimativas: benfeitoriaAvaliacao.benfeitoriaImovel.estimativas,
                                valor: benfeitoriaAvaliacao.benfeitoriaImovel.valor,
                                estadoConservacao: benfeitoriaAvaliacao.benfeitoriaImovel.estadoConservacao,
                                valorEdificacao: benfeitoriaAvaliacao.benfeitoriaImovel.valorEdificacao
                            });
                            if (benfeitoriaAvaliacao.benfeitoriaImovel.estimativas && benfeitoriaAvaliacao.benfeitoriaImovel.estimativas.length > 0) {
                                vm.isMotivosEstimativa = true;
                            }
                        });
                    } else if (vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao) {
                        vm.listaTerrenosParcela.push({
                            id: vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.codigo,
                            tipo: vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.tipoEdificacao.descricao,
                            descricao: vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.tipoProjeto.descricao,
                            areaConstruida: vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.area,
                            vidaUtil: vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.vidaUtil,
                            idadeAparente: vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.idadeAparente,
                            padraoAcabamento: vm.listaPadraoAcabamento[vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.padraoAcabamento],
                            tipoProjeto: vm.listaTiposProjeto[vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.tipoProjetoCub],
                            estimativas: vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.estimativas,
                            valor: vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.valor,
                            estadoConservacao: vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.estadoConservacao,
                            valorEdificacao: vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.valorEdificacao
                        });
                        if (vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.estimativas && vm.laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.estimativas.length > 0) {
                            vm.isMotivosEstimativa = true;
                        }
                    }
                };

                vm.redirecionarConsultarLaudoRelatorio = function () {
                    $state.go('avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO');
                };

                vm.assinarLaudoRelatorio = function () {
                    laudoRelatorioService.assinarLaudoRelatorio(vm.idLaudo).then(function () {
                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-assinatura-registrada-sucesso'));
                        $state.go('avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO');
                    });
                };

                var validarValorLaudoRelatorio = function (tipoValor, resultadoAvaliacao) {
                    if (tipoValor == VALOR_MEDIO) {
                        return resultadoAvaliacao.valorMedio;
                    } else if (tipoValor == VALOR_MINIMO) {
                        return resultadoAvaliacao.valorMinimo;
                    } else if (tipoValor == VALOR_MAXIMO) {
                        return resultadoAvaliacao.valorMaximo;
                    } else if (tipoValor == OUTRO_VALOR) {
                        return resultadoAvaliacao.outroValorAdotado;
                    }
                };

                vm.formatarValorMonetario = function (valor) {
                    if (valor) {
                        return ($filter('currency')(valor)).replace('R$', '');
                    }
                };

                vm.convertNumeroPorExtenso = function (numero) {
                    if (numero) {
                        return numeroExtensoAvaliacaoService.converterNumeroPorExtenso(numero, (vm.laudoRelatorio.avaliacao ? 'real' : ''));
                    }
                };

                vm.gerarImagem = function (base64, contentType) {
                    return imagemAvaliacaoService.gerarPreviewImagem(base64, contentType);
                };

                var buscarDadosProprietarioImovel = function (idImovel) {
                    imovelAvaliacaoService.buscarDadosDetalharProprietarioLaudo(idImovel).then(function (resp) {
                        vm.dadosProprietario = resp.data;
                    });
                };

                var buscarFerramentasAnaliticas = function () {
                    identificacaoLaudoRelatorioService.buscarFerramentasAnaliticas().then(function (resp) {
                        vm.ferramentasAnaliticas = resp.data;
                    });
                };

                var buscarImagensImovel = function (idImovel) {
                    imagemAvaliacaoService.recuperarImagensImovel(idImovel).then(function (resp) {
                        vm.listaImagensImovel = resp.data;
                    });
                };

                var capturarImagemLocalizacao = function () {
                    var imovel = {};
                    $timeout(function () {
                        domtoimage.toPng(document.getElementById('mapaLocalizacao')).then(function (dataUrl) {
                            imovel.id = vm.laudoRelatorio.detalhesImovel.imovel.id;
                            imovel.imagemLocalizacao = dataUrl.replace('data:image/png;base64,', '');
                            laudoRelatorioService.salvarImagemLocalizacao(imovel).then(function () {
                            });
                            $scope.$apply();
                        });
                    }, 1000);
                };

                var capturarImagemMapaAmostras = function () {
                    var mapa = {};
                    $timeout(function () {
                        domtoimage.toPng(document.getElementById('mapaAmostras')).then(function (dataUrl) {
                            mapa.id = vm.laudoRelatorio.avaliacao.id;
                            mapa.imagemMapaAmostras = dataUrl.replace('data:image/png;base64,', '');
                            laudoRelatorioService.salvarImagemMapaAmostras(mapa).then(function () {
                            });
                            $scope.$apply();
                        });
                    }, 1000);
                };

                var buscarImagensBenfeitorias = function (idBenfeitorias) {
                    imagemAvaliacaoService.recuperarImagensBenfeitorias(idBenfeitorias).then(function (resp) {
                        vm.listaImagensBenfeitorias = resp.data;
                    });
                };

                var buscarImagensUnidadeAutonoma = function (idUnidadeAutonoma) {
                    imagemAvaliacaoService.recuperarImagensUnidadeAutonoma(idUnidadeAutonoma).then(function (resp) {
                        vm.listaimagensUnidadeAutonoma = resp.data;
                    });
                };

                var buscarDocumentosCartoriais = function (idDetalhesImovel) {
                    laudoRelatorioService.recuperarDocumentosCartoriais(idDetalhesImovel).then(function (resp) {
                        vm.listaDocumentosCartoriais = resp.data;
                        angular.forEach(vm.listaDocumentosCartoriais, function (documentoCartorial) {
                            documentoCartorial.sce = $sce.trustAsResourceUrl(vm.gerarImagem(documentoCartorial.binario, documentoCartorial.arquivo.contentType));
                        });
                    });
                };

                var buscarDocumentosParametrosUrbanisticos = function (idTerrenoImovel) {
                    laudoRelatorioService.recuperarDocumentosParametrosUrbanisticos(idTerrenoImovel).then(function (resp) {
                        vm.listaDocumentosParametrosUrbanisticos = resp.data;
                        angular.forEach(vm.listaDocumentosParametrosUrbanisticos, function (docParametroUrbanistico) {
                            docParametroUrbanistico.sce = $sce.trustAsResourceUrl(vm.gerarImagem(docParametroUrbanistico.anexo, docParametroUrbanistico.arquivo.contentType));
                        });
                    });
                };

                var buscarAnexosAvaliacao = function (idAvaliacao) {
                    avaliacaoLaudoService.recuperarAnexosAvaliacao(idAvaliacao).then(function (resp) {
                        vm.listaAnexosAvaliacao = resp.data;
                        angular.forEach(vm.listaAnexosAvaliacao, function (anexoAvaliacao) {
                            anexoAvaliacao.sce = $sce.trustAsResourceUrl(vm.gerarImagem(anexoAvaliacao.anexo, anexoAvaliacao.arquivo.contentType));
                        });
                    });
                };

                var buscarMetodosAvaliacao = function () {
                    identificacaoLaudoRelatorioService.buscarMetodosAvaliacao().then(function (resp) {
                        vm.metodosAvaliacao = resp.data;
                    });
                };

                var buscarTratamentosIdentificacao = function () {
                    identificacaoLaudoRelatorioService.buscarTratamentosIdentificacao().then(function (resp) {
                        vm.tratamentosIdentificacao = resp.data;
                    });
                };

                var buscarQuantificacaoCustoBenfeitorias = function (callback) {
                    identificacaoLaudoRelatorioService.buscarQuantificacaoCustoBenfeitorias().then(function (resp) {
                        vm.listaQuantificacaoCustoBenfeitorias = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                var buscarMetodologia = function () {
                    identificacaoLaudoRelatorioService.buscarMetodologia().then(function (resp) {
                        vm.listaMetodologia = resp.data;
                    });
                };

                var pesquisarPadroesAcabamento = function () {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function (resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function () {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function (resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                var pesquisarMotivosEstimativaValor = function () {
                    cubAvaliacaoService.buscarMotivosEstimativaValor().then(function (resp) {
                        vm.listaMotivosEstimativa = resp.data;
                    });
                };

                var pesquisarVariaveisEnum = function () {
                    avaliacaoLaudoService.buscarVariaveisEnum().then(function (resp) {
                        vm.listaVariaveis = resp.data;
                    });
                };

                var pesquisarTiposVariaveis = function () {
                    avaliacaoLaudoService.buscarTiposVariavel().then(function (resp) {
                        vm.listaTiposVariavel = resp.data;
                    });
                };

                var buscarAmostras = function () {
                    if (vm.laudoRelatorio.avaliacao.id) {
                        avaliacaoLaudoService.buscarAmostras(vm.laudoRelatorio.avaliacao.id).then(function (resp) {
                            vm.variaveisAmostras = resp.data;
                            var j = 0;
                            if (vm.variaveisAmostras && vm.variaveisAmostras.length > j) {
                                vm.amostras.push(vm.variaveisAmostras[j].amostra);
                                vm.amostras[j].variaveisAmostras = [];
                            }
                            for (var i = 0; i < vm.variaveisAmostras.length; i++) {
                                if (vm.variaveisAmostras[i].amostra.id !== vm.amostras[j].id) {
                                    vm.amostras.push(vm.variaveisAmostras[i].amostra);
                                    j++;
                                    vm.amostras[j].variaveisAmostras = [];
                                    vm.amostras[j].variaveisAmostras.push(vm.variaveisAmostras[i]);
                                } else {
                                    vm.amostras[j].variaveisAmostras.push(vm.variaveisAmostras[i]);
                                }
                            }

                            if (vm.amostras.length > 0) {
                                loadMapas(function () {
                                    vm.carregarMapaAmostras = true;
                                    $timeout(function () {
                                        marcarPontosMapa(vm.amostras)
                                    }, 1000);
                                });
                            }
                        });
                    }
                };

                var marcarPontosMapa = function (amostras) {

                    var variaveisAmostras = {};
                    variaveisAmostras.amostras = [];
                    variaveisAmostras.imovel = {};
                    variaveisAmostras.amostras = angular.copy(amostras);
                    variaveisAmostras.imovel = angular.copy(vm.imovelMapa);
                    NotificadorService.notify(vm, variaveisAmostras, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_PONTOS_AVALIACAO_TERRENO);

                    capturarImagemMapaAmostras();
                };

                var buscarPonto = function (ripImovel) {

                    imovelAvaliacaoService.consultarImovelPorRip(ripImovel).then(function (resposta) {
                        vm.imovelMapa = resposta.data;
                        if (vm.imovelMapa) {

                            imovelAvaliacaoService.buscarBenfeitoriasSuPorRip(ripImovel).then(function (resp) {
                                vm.imovelMapa.benfeitorias = resp.data;
                                vm.carregarMapa = true;
                                loadMapas(function () {
                                    $timeout(function () {
                                        marcarPontoMapa(vm.imovelMapa);
                                    }, 1000);
                                });
                            }, function () {
                                vm.carregarMapa = true;
                                loadMapas(function () {
                                    $timeout(function () {
                                        marcarPontoMapa(vm.imovelMapa);
                                    }, 1000);
                                });
                            });

                        }
                    });
                };

                function loadMapas(callback) {

                    ciMapaAvaliacaoLayersFactory.init();
                    ciMapaAvaliacaoViewFactory.init();
                    if (callback) {
                        $timeout(function () {
                            callback();
                        });
                    }
                };

                var marcarPontoMapa = function (imovel) {
                    NotificadorService.notify(vm, imovel.pontoImovel.coordinates, MAPA_CONSTANTS.NOTIFICADORES.ATUALIZAR_COORDENADAS_DO_PONTO);
                    NotificadorService.notify(vm, imovel, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_PONTO_POR_COORDENADAS);

                    capturarImagemLocalizacao();
                };


                vm.medirGrauEnquadramentoFundamentacao = function () {
                    if (vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado
                        && vm.laudoRelatorio.avaliacao.enquadramento.quantidadeMinimaDados
                        && vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado
                        && vm.laudoRelatorio.avaliacao.enquadramento.extrapolacao
                        && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao
                        && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido) {
                        if (vm.laudoRelatorio.avaliacao.enquadramento.isApresentacaoLaudoModalidade
                            && vm.laudoRelatorio.avaliacao.enquadramento.isApresentacaoAnaliseLaudo
                            && vm.laudoRelatorio.avaliacao.enquadramento.isIdenticacaoCompletaEnderecos
                            && (vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado === TRES || vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado === DOIS)
                            && vm.laudoRelatorio.avaliacao.enquadramento.quantidadeMinimaDados === TRES
                            && (vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado === TRES || vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado === DOIS)
                            && vm.laudoRelatorio.avaliacao.enquadramento.extrapolacao === TRES
                            && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao === TRES
                            && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido === TRES) {
                            vm.enquadramentoGrau3 = true;
                            vm.enquadramentoGrau2 = false;
                            vm.enquadramentoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else if ((vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado === UM || vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado === TRES)
                            && (vm.laudoRelatorio.avaliacao.enquadramento.quantidadeMinimaDados === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.quantidadeMinimaDados === TRES)
                            && (vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado === UM || vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado === TRES)
                            && (vm.laudoRelatorio.avaliacao.enquadramento.extrapolacao === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.extrapolacao === TRES)
                            && (vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao === TRES)
                            && (vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido === TRES)) {
                            vm.enquadramentoGrau3 = false;
                            vm.enquadramentoGrau2 = true;
                            vm.enquadramentoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else if (vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado !== vm.NAO_ATINGIDO
                            && vm.laudoRelatorio.avaliacao.enquadramento.quantidadeMinimaDados !== vm.NAO_ATINGIDO
                            && vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado !== vm.NAO_ATINGIDO
                            && vm.laudoRelatorio.avaliacao.enquadramento.extrapolacao !== vm.NAO_ATINGIDO
                            && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao !== vm.NAO_ATINGIDO
                            && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido !== vm.NAO_ATINGIDO) {
                            vm.enquadramentoGrau3 = false;
                            vm.enquadramentoGrau2 = false;
                            vm.enquadramentoGrau1 = true;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else {
                            vm.enquadramentoGrau3 = false;
                            vm.enquadramentoGrau2 = false;
                            vm.enquadramentoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = true;
                        }
                    }
                };

                vm.downloadLaudoRelatorioAvaliacao = function () {
                    return laudoRelatorioService.downloadLaudoRelatorioAvaliacao(vm.idLaudo);
                };

                var calcularGrauMetodoEvolutivo = function () {
                    vm.enquadramentoGrauMetodoEvolutivo1 = false;
                    vm.enquadramentoGrauMetodoEvolutivo2 = false;
                    vm.enquadramentoGrauMetodoEvolutivo3 = false;

                    if (vm.laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria && vm.laudoRelatorio.quantificacaoCusto && vm.laudoRelatorio.quantificacaoCusto.tipoBdi && vm.laudoRelatorio.quantificacaoCusto.tipoDepreciacao) {
                        if (vm.laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria === 'ORCAMENTO' && vm.laudoRelatorio.quantificacaoCusto.tipoBdi !== 'ARBITRADO' && vm.laudoRelatorio.quantificacaoCusto.tipoDepreciacao !== 'ARBITRADO') {
                            vm.enquadramentoGrauMetodoEvolutivo3 = true;
                        } else if (vm.laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria !== 'CUB_PROJETO_DIFERENTE' && vm.laudoRelatorio.quantificacaoCusto.tipoBdi !== 'ARBITRADO') {
                            vm.enquadramentoGrauMetodoEvolutivo2 = true;
                        } else {
                            vm.enquadramentoGrauMetodoEvolutivo1 = true;
                        }
                    }
                };

                var calcularValorTotalCustoBenfeitoria = function () {
                    vm.valorCustoTotalBenfeitoria = 0;
                    angular.forEach(vm.laudoRelatorio.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        vm.valorCustoTotalBenfeitoria += benfeitoria.valorCalculado;
                    });
                };

                var buscarValorTerreno = function () {
                    if (vm.laudoRelatorio.avaliacao) {
                        if (vm.laudoRelatorio.identificacao.metodologia !== 'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA') {
                            determinarValorAdotadoSelecionado();
                        } else {
                            vm.valorTerreno = vm.laudoRelatorio.avaliacao.valorTerreno;
                        }
                    }
                };

                var determinarValorAdotadoSelecionado = function () {
                    if (vm.laudoRelatorio.avaliacao.resultadosAvaliacao.tipoValor === 'VALOR_MEDIO') {
                        vm.valorTerreno = vm.laudoRelatorio.avaliacao.resultadosAvaliacao.valorMedio;
                    } else if (vm.laudoRelatorio.avaliacao.resultadosAvaliacao.tipoValor === 'VALOR_MINIMO') {
                        vm.valorTerreno = vm.laudoRelatorio.avaliacao.resultadosAvaliacao.valorMinimo;
                    } else if (vm.laudoRelatorio.avaliacao.resultadosAvaliacao.tipoValor === 'VALOR_MAXIMO') {
                        vm.valorTerreno = vm.laudoRelatorio.avaliacao.resultadosAvaliacao.valorMaximo;
                    } else if (vm.laudoRelatorio.avaliacao.resultadosAvaliacao.tipoValor === 'OUTRO_VALOR') {
                        vm.valorTerreno = vm.laudoRelatorio.avaliacao.resultadosAvaliacao.outroValorAdotado;
                    }
                };

                vm.calcularGrauFundamentacaoMetodoEvolutivo = function () {

                    if (vm.valorTerreno && vm.laudoRelatorio.resultadoAvaliacao.valorImovel > 0) {
                        if (vm.valorTerreno < (vm.laudoRelatorio.resultadoAvaliacao.valorImovel * 0.15)) {
                            vm.itemEstimativaValorTerrenoGrau2 = true;
                        } else {
                            vm.medirGrauEnquadramentoFundamentacao();
                        }
                        if ((vm.laudoRelatorio.resultadoAvaliacao.valorImovel - vm.valorTerreno) < (vm.laudoRelatorio.resultadoAvaliacao.valorImovel * 0.15)) {
                            vm.itemEstimativaCustosReedicaoGrau2 = true;
                        } else {
                            vm.calcularEnquadramentoGrauFundamentacao();
                        }
                    }

                    if (vm.laudoRelatorio.resultadoAvaliacao.tipoFatorComercializacao === 'INFERIDO_MERCADO_SEMELHANTE') {
                        vm.itemFatorComercializacaoGrau3 = true;
                        vm.itemFatorComercializacaoGrau2 = false;
                        vm.itemFatorComercializacaoGrau1 = false;
                    } else if (vm.laudoRelatorio.resultadoAvaliacao.tipoFatorComercializacao === 'JUSTIFICADO') {
                        vm.itemFatorComercializacaoGrau3 = false;
                        vm.itemFatorComercializacaoGrau2 = true;
                        vm.itemFatorComercializacaoGrau1 = false;
                    } else if (vm.laudoRelatorio.resultadoAvaliacao.tipoFatorComercializacao === 'ARBITRADO') {
                        vm.itemFatorComercializacaoGrau3 = false;
                        vm.itemFatorComercializacaoGrau2 = false;
                        vm.itemFatorComercializacaoGrau1 = true;
                    }

                    vm.calcularEnquadramentoGrauFundamentacaoMetodoEvolutivo();
                };

                vm.calcularEnquadramentoGrauFundamentacaoMetodoEvolutivo = function () {
                    if (vm.itemEstimativaValorTerrenoGrau3 && vm.itemEstimativaCustosReedicaoGrau3 && (vm.itemFatorComercializacaoGrau3 || vm.itemFatorComercializacaoGrau2)) {
                        vm.enquadramentoGrau3 = true;
                    } else if ((vm.itemEstimativaValorTerrenoGrau2 || vm.itemEstimativaValorTerrenoGrau3) && (vm.itemEstimativaCustosReedicaoGrau3 || vm.itemEstimativaCustosReedicaoGrau2)) {
                        vm.enquadramentoGrau2 = true;
                    } else if ((vm.itemEstimativaValorTerrenoGrau3 || vm.itemEstimativaValorTerrenoGrau2 || vm.itemEstimativaValorTerrenoGrau1) &&
                        (vm.itemEstimativaCustosReedicaoGrau3 || vm.itemEstimativaCustosReedicaoGrau2 || vm.itemEstimativaCustosReedicaoGrau1) &&
                        (vm.itemFatorComercializacaoGrau3 || vm.itemFatorComercializacaoGrau2 || vm.itemFatorComercializacaoGrau1)) {
                        vm.enquadramentoGrau1 = true;
                    }
                };

                vm.calcularEnquadramentoGrauFundamentacao = function () {
                    if (vm.laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria && vm.laudoRelatorio.quantificacaoCusto && vm.laudoRelatorio.quantificacaoCusto.tipoBdi && vm.laudoRelatorio.quantificacaoCusto.tipoDepreciacao) {
                        if (vm.laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria === 'ORCAMENTO' && vm.laudoRelatorio.quantificacaoCusto.tipoBdi !== 'ARBITRADO' && vm.laudoRelatorio.quantificacaoCusto.tipoDepreciacao !== 'ARBITRADO') {
                            vm.itemEstimativaCustosReedicaoGrau3 = true;
                        } else if (vm.laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria !== 'CUB_PROJETO_DIFERENTE' && vm.laudoRelatorio.quantificacaoCusto.tipoBdi !== 'ARBITRADO') {
                            vm.itemEstimativaCustosReedicaoGrau2 = true;
                        } else {
                            vm.itemEstimativaCustosReedicaoGrau1 = true;
                        }
                    }
                };

                vm.medirGrauEnquadramentoFundamentacao = function () {
                    if (vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado
                        && vm.laudoRelatorio.avaliacao.enquadramento.quantidadeMinimaDados
                        && vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado
                        && vm.laudoRelatorio.avaliacao.enquadramento.extrapolacao
                        && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao
                        && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido) {
                        if (vm.laudoRelatorio.avaliacao.enquadramento.isApresentacaoLaudoModalidade
                            && vm.laudoRelatorio.avaliacao.enquadramento.isApresentacaoAnaliseLaudo
                            && vm.laudoRelatorio.avaliacao.enquadramento.isIdenticacaoCompletaEnderecos
                            && (vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado === TRES || vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado === DOIS)
                            && vm.laudoRelatorio.avaliacao.enquadramento.quantidadeMinimaDados === TRES
                            && (vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado === TRES || vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado === DOIS)
                            && vm.laudoRelatorio.avaliacao.enquadramento.extrapolacao === TRES
                            && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao === TRES
                            && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido === TRES) {
                            vm.itemEstimativaValorTerrenoGrau3 = true;
                            vm.itemEstimativaValorTerrenoGrau2 = false;
                            vm.itemEstimativaValorTerrenoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else if ((vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado === UM || vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado === TRES)
                            && (vm.laudoRelatorio.avaliacao.enquadramento.quantidadeMinimaDados === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.quantidadeMinimaDados === TRES)
                            && (vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado === UM || vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado === TRES)
                            && (vm.laudoRelatorio.avaliacao.enquadramento.extrapolacao === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.extrapolacao === TRES)
                            && (vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao === TRES)
                            && (vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido === DOIS || vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido === TRES)) {
                            vm.itemEstimativaValorTerrenoGrau3 = false;
                            vm.itemEstimativaValorTerrenoGrau2 = true;
                            vm.itemEstimativaValorTerrenoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else if (vm.laudoRelatorio.avaliacao.enquadramento.caracterizacaoImovelAvaliado !== vm.NAO_ATINGIDO
                            && vm.laudoRelatorio.avaliacao.enquadramento.quantidadeMinimaDados !== vm.NAO_ATINGIDO
                            && vm.laudoRelatorio.avaliacao.enquadramento.identificacaoDadosMercado !== vm.NAO_ATINGIDO
                            && vm.laudoRelatorio.avaliacao.enquadramento.extrapolacao !== vm.NAO_ATINGIDO
                            && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao !== vm.NAO_ATINGIDO
                            && vm.laudoRelatorio.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido !== vm.NAO_ATINGIDO) {
                            vm.itemEstimativaValorTerrenoGrau3 = false;
                            vm.itemEstimativaValorTerrenoGrau2 = false;
                            vm.itemEstimativaValorTerrenoGrau1 = true;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else {
                            vm.itemEstimativaValorTerrenoGrau3 = false;
                            vm.itemEstimativaValorTerrenoGrau2 = false;
                            vm.itemEstimativaValorTerrenoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = true;
                        }
                    }
                };

                var tipoValorTerrenoAvaliacao = function () {
                    if (vm.laudoRelatorio.avaliacao.tipoValorTerrenoAvaliacao !== 'OUTRA_FONTE') {
                        avaliacaoLaudoService.buscarValorTerreno(vm.laudoRelatorio.identificacao.rip, vm.laudoRelatorio.avaliacao.tipoValorTerrenoAvaliacao).then(function (resp) {
                            vm.pvg = resp.data;
                        });
                    }
                };

                var buscarExecucoesVistoria = function () {
                    laudoRelatorioService.buscarExecucoesVistoria().then(function (resp) {
                        vm.listaExecucaoVistoria = resp.data;
                    });
                };


                init();
            }])

})();

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('detalharLaudoRelatorioAvaliacaoController',
            ["$scope", "laudoRelatorioService", "$stateParams", "imovelAvaliacaoService", "$state", "$filter", "ComumFactory", "dominioAvaliacaoService", "benfeitoriasLaudoRelatorioService", "unidadeAutonomaAvaliacaoService", "validacaoLaudoRelatorioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "$mdDialog", "imagemAvaliacaoService", "avaliacaoLaudoService", "$timeout", function ($scope, laudoRelatorioService, $stateParams,
                      imovelAvaliacaoService, $state, $filter, ComumFactory, dominioAvaliacaoService,
                      benfeitoriasLaudoRelatorioService, unidadeAutonomaAvaliacaoService, validacaoLaudoRelatorioAvaliacaoService,
                      avaliacaoEscopoCompartilhadoService, $mdDialog, imagemAvaliacaoService, avaliacaoLaudoService, $timeout) {

                var vm = $scope;

                vm.IDENTIFICACAO = 'IDENTIFICACAO';
                vm.DETALHES_IMOVEL = 'DETALHES_IMOVEL';
                vm.TERRENO_IMOVEL = 'TERRENO_IMOVEL';
                vm.BENFEITORIAS = 'BENFEITORIAS';
                vm.UNIDADE_AUTONOMA = 'UNIDADE_AUTONOMA';
                vm.UTILIZACAO = 'UTILIZACAO';
                vm.VISTORIA = 'VISTORIA';
                vm.AVALIACAO = 'AVALIACAO';
                vm.RESULTADO = 'RESULTADO';
                vm.RESULTADO_RELATORIO = 'RESULTADO_RELATORIO';
                vm.ANEXOS = 'ANEXOS';
                vm.CANCELAMENTO = 'CANCELAMENTO';

                vm.carregaVistoria = false;

                var RETORNAR = 'RETORNAR';
                var AVANCAR = 'AVANCAR';

                vm.RETORNAR = 'RETORNAR';
                vm.AVANCAR = 'AVANCAR';

                vm.HECTARE = 'HECTARE';

                vm.formularioIdentificacao = null;
                vm.formularioDetalhesImovel = null;
                vm.formularioTerrenoImovel = null;
                vm.formularioBenfeitorias = null;
                vm.formularioUnidadeAutonoma = null;
                vm.formularioUtilizacao = null;
                vm.formularioVistoria = null;
                vm.formularioAvaliacao = null;
                vm.formularioResultado = null;
                vm.formularioAnexo = null;

                vm.ripImovel = null;
                vm.imovel = {};
                vm.listaResponsaveisTecnicos = [];
                vm.listaNormasUtilizadas = [];
                vm.listaMatriculasTranscricao = [];
                vm.listaCertidoesCartoriais = [];
                vm.documentosParametrosUrbanisticos = [];
                vm.listaBenfeitoriaAvaliacao = [];
                vm.terreno = {};
                vm.unidadeAutonomaIncorporacao = null;
                vm.listaFotosImovel = [];
                vm.listaFotosBenfeitorias = [];
                vm.listaFotosUnidadeAutonoma = [];

                vm.vistoriaListaFotosImovel = [];
                vm.vistoriaListaFotosBenfeitorias = [];
                vm.vistoriaListaFotosUnidadeAutonoma = [];
                vm.mostrarResultado = false;

                vm.pontoImovel = null;
                vm.dadosEnderecamento = {};
                vm.dadosProprietario = {};

                vm.CONTENT_TYPE_02 = 'CONTENT_TYPE_02';

                vm.isSalvoRascunho = false;
                vm.detalharLaudoRelatorio = true;

                var init = function () {

                    if (avaliacaoEscopoCompartilhadoService.getObjeto('editar')) {
                        vm.tabSelect = vm.BENFEITORIAS;
                    } else if (avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect')) {
                        vm.tabSelect = avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect');
                    } else {
                        vm.tabSelect = vm.IDENTIFICACAO;
                    }

                    inicializarLaudoRelatorio(null);
                    if ($stateParams.idLaudo) {
                        vm.carregaVistoria = true;
                        buscarDadosEditarLaudoRelatorioAvaliacaoEditar($stateParams.idLaudo);
                    } else if (avaliacaoEscopoCompartilhadoService.getObjeto('ripImovel') && avaliacaoEscopoCompartilhadoService.getObjeto('laudoRelatorioAvaliacao')) {
                        recuperarValoresLaudoRelatorioInformarCub();
                        buscarListaBenfeitoriasSu($stateParams.rip);
                    } else if ($stateParams.rip) {
                        vm.ripImovel = $stateParams.rip;
                        vm.buscarDadosImovelPorRip($stateParams.rip);
                    }
                };

                vm.buscarDadosImovelPorRip = function (ripImovel) {
                    var idsBenfeitorias = [];
                    imovelAvaliacaoService.buscarDadosDetalharImovelLaudoRelatorioRip(ripImovel)
                        .then(function (resposta) {
                            if (resposta.data && resposta.data != '') {
                                inicializarLaudoRelatorio(ripImovel);
                                vm.imovel = resposta.data;
                                vm.imovel.id = null;
                                vm.pontoImovel = vm.imovel.pontoImovel;
                                vm.ripImovel = ripImovel;
                                vm.dadosEnderecamento = vm.imovel.enderecamento;
                                vm.dadosProprietario = vm.imovel.proprietario;
                                vm.listaMatriculasTranscricao = vm.imovel.matriculas;
                                vm.listaBenfeitoriaAvaliacao = vm.imovel.benfeitorias;
                                vm.unidadeAutonomaIncorporacao = vm.imovel.unidadeAutonoma;
                                vm.laudoRelatorioAvaliacao.terrenoImovel.terreno.areaTotal = vm.imovel.terrenoImovel.areaTotalCadastral;

                                for (var i = 0; i < vm.imovel.certidoesCartorial.length; i++) {
                                    if (vm.imovel.certidoesCartorial[i].arquivo && !vm.imovel.certidoesCartorial[i].arquivo.nome && !vm.imovel.certidoesCartorial[i].arquivo.contentType && !vm.imovel.certidoesCartorial[i].nome && !vm.imovel.certidoesCartorial[i].idCartorio) {
                                        vm.imovel.certidoesCartorial.splice(i, i);
                                    } else {
                                        vm.listaCertidoesCartoriais.push(vm.imovel.certidoesCartorial[i]);
                                    }
                                }


                                angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                                    benfeitoria.benfeitoriaImovel = angular.copy(benfeitoria);
                                    idsBenfeitorias.push(benfeitoria.id);
                                    benfeitoria.benfeitoriaImovel.idOriginal = angular.copy(benfeitoria.id);
                                    benfeitoria.id = null;

                                    if (benfeitoria.benfeitoriaImovel.edificacao.documentacao) {
                                        for (var j = 0; j < benfeitoria.benfeitoriaImovel.edificacao.documentacao.length; j++) {
                                            benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].id = null;
                                            benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].isPossui = benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].isPossui === null ? 'SEM_INFORMACAO' : benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].isPossui;
                                            if (benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].arquivo) {
                                                benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].arquivo = null;
                                            }
                                        }
                                    }
                                });

                                angular.forEach(vm.listaCertidoesCartoriais, function (certidaoCartorial) {
                                    certidaoCartorial.arquivo.id = null;
                                });

                                if (vm.unidadeAutonomaIncorporacao) {
                                    vm.unidadeAutonomaIncorporacao.idOriginal = angular.copy(vm.unidadeAutonomaIncorporacao.id);
                                    vm.unidadeAutonomaIncorporacao.id = null;
                                }
                                buscarImagensImovelPorRip(ripImovel);
                                buscarImagensBenfeitorias(idsBenfeitorias);
                                buscarImagensUnidadeAutonoma(vm.unidadeAutonomaIncorporacao);
                                buscarListaBenfeitoriasSu(ripImovel);

                            } else {
                                vm.ripImovel = null;
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-rip-invalido'));
                            }
                        }, function () {
                            vm.ripImovel = null;
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-rip-invalido'));
                        });
                };

                var buscarListaBenfeitoriasSu = function (rip) {
                    imovelAvaliacaoService.buscarBenfeitoriasSuPorRip(rip).then(function (resp) {
                        vm.benfeitoriasTemp = resp.data;

                        for (var i = 0; i < vm.benfeitoriasTemp.length; i++) {
                            if (!vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.edificacao.documentacao) {
                                vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.edificacao.documentacao = [];
                                for (var j = 0; j < vm.benfeitoriasTemp[i].edificacao.documentacao.length; j++) {
                                    if (vm.benfeitoriasTemp[i].edificacao.documentacao[j].arquivo) {
                                        vm.benfeitoriasTemp[i].edificacao.documentacao[j].arquivo.id = null;
                                    }
                                    vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.edificacao.documentacao.push({
                                        nome: vm.benfeitoriasTemp[i].edificacao.documentacao[j].nome,
                                        dtEmissao: vm.benfeitoriasTemp[i].edificacao.documentacao[j].dataEmissao,
                                        dtValidade: vm.benfeitoriasTemp[i].edificacao.documentacao[j].dataValidade,
                                        arquivo: vm.benfeitoriasTemp[i].edificacao.documentacao[j].arquivo,
                                        isPossui: vm.benfeitoriasTemp[i].edificacao.documentacao[j].possui === null ? null : vm.benfeitoriasTemp[i].edificacao.documentacao[j].possui.name,
                                    });
                                }
                            } else {
                                for (j = 0; j < vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.edificacao.documentacao.length; j++) {
                                    vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.edificacao.documentacao[j].id = null;
                                    if (vm.benfeitoriasTemp[i].edificacao.documentacao[j].arquivo) {
                                        vm.benfeitoriasTemp[i].edificacao.documentacao[j].arquivo = null;
                                    }
                                }
                            }
                        }

                    });
                };

                var buscarImagensImovelPorRip = function (ripImovel) {
                    imovelAvaliacaoService.buscarFotosImovel(ripImovel).then(function (resp) {
                        vm.listaFotosImovel = resp.data;
                    });
                };

                var buscarImagensBenfeitorias = function (idsBenfeitorias) {
                    if (idsBenfeitorias && idsBenfeitorias.length > 0) {
                        benfeitoriasLaudoRelatorioService.buscarFotosBenfeitorias(idsBenfeitorias.toString())
                            .then(function (imagens) {
                                vm.listaFotosBenfeitorias = imagens.data;
                            });
                    }
                };

                var buscarImagensUnidadeAutonoma = function (unidadeAutonoma) {
                    if (unidadeAutonoma) {
                        unidadeAutonomaAvaliacaoService.buscarImagensUnidadeAutonoma(unidadeAutonoma.id)
                            .then(function (resp) {
                                vm.listaFotosUnidadeAutonoma = resp.data;
                            });
                    }
                };

                var inicializarLaudoRelatorio = function (ripImovel) {
                    vm.laudoRelatorioAvaliacao = {
                        identificacao: {rip: ripImovel},
                        detalhesImovel: {imovel: {}},
                        terrenoImovel: {terreno: {}, documentosParametrosUrbanisticos: []},
                        benfeitorias: {benfeitoriasAvaliacao: []},
                        unidadeAutonoma: {unidadeAutonomaIncorporacao: {}},
                        utilizacao: {},
                        vistoria: {},
                        avaliacao: {enquadramento: {isAdocaoEstimativaTendencia: true}}
                    };
                };

                var recuperarValoresLaudoRelatorioInformarCub = function () {
                    vm.laudoRelatorioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoRelatorioAvaliacao');
                    vm.dadosEnderecamento = avaliacaoEscopoCompartilhadoService.getObjeto('dadosEnderecamento');
                    vm.dadosProprietario = avaliacaoEscopoCompartilhadoService.getObjeto('dadosProprietario');

                    /**Identificacao**/
                    vm.ripImovel = avaliacaoEscopoCompartilhadoService.getObjeto('ripImovel');
                    vm.pontoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('pontoImovel');
                    vm.listaResponsaveisTecnicos = avaliacaoEscopoCompartilhadoService.getObjeto('listaResponsaveisTecnicos');
                    vm.listaNormasUtilizadas = avaliacaoEscopoCompartilhadoService.getObjeto('listaNormasUtilizadas');
                    /**Detalher Imovel**/
                    vm.listaMatriculasTranscricao = avaliacaoEscopoCompartilhadoService.getObjeto('listaMatriculasTranscricao');
                    vm.listaCertidoesCartoriais = avaliacaoEscopoCompartilhadoService.getObjeto('listaCertidoesCartoriais');
                    angular.forEach(vm.listaCertidoesCartoriais, function (certidaoCartorial) {
                        certidaoCartorial.arquivo.id = null;
                    });
                    vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                    vm.listaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosImovel');
                    /**Terreno Imovel**/
                    vm.terreno = avaliacaoEscopoCompartilhadoService.getObjeto('terreno');
                    vm.documentosParametrosUrbanisticos = avaliacaoEscopoCompartilhadoService.getObjeto('documentosParametrosUrbanisticos');
                    /**Benfeitorias**/
                    vm.listaBenfeitoriaAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriaAvaliacao');
                    vm.listaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosBenfeitorias');
                    /**Unidade Autonoma**/
                    vm.unidadeAutonomaIncorporacao = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaIncorporacao');
                    vm.listaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosUnidadeAutonoma');
                    /**Vistoria**/
                    vm.vistoriaListaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosImovel');
                    vm.vistoriaListaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosBenfeitorias');
                    vm.vistoriaListaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosUnidadeAutonoma');
                    /**Avaliacao**/
                    vm.tabSelect = avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect');
                    vm.laudoRelatorioAvaliacao.avaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao');

                    vm.formularioIdentificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioIdentificacao');
                    vm.formularioDetalhesImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioDetalhesImovel');
                    vm.formularioTerrenoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioTerrenoImovel');
                    vm.formularioBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('formularioBenfeitorias');
                    vm.formularioUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUnidadeAutonoma');
                    vm.formularioUtilizacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUtilizacao');
                    vm.formularioVistoria = avaliacaoEscopoCompartilhadoService.getObjeto('formularioVistoria');
                    vm.formularioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAvaliacao');
                    vm.formularioResultado = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultado');

                    avaliacaoEscopoCompartilhadoService.limparEscopo();
                };

                vm.redirecionarConsultarImovelLaudoRelatorio = function () {
                    $state.go('avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO');
                };

                var validarCreaResponsaveisTecnicosInformados = function () {
                    var creaPendente = false;
                    if (vm.listaResponsaveisTecnicos.length == 0) {
                        creaPendente = true;
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-necessario-informar-crea-responsavel'));
                    }
                    angular.forEach(vm.listaResponsaveisTecnicos, function (responsavelTecnico) {
                        if (!responsavelTecnico.crea) {
                            creaPendente = true;
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-necessario-informar-crea-responsavel'));
                        }
                    });

                    return creaPendente;
                };

                var preencherDadosAbasLaudoRelatorio = function () {
                    /**Vistoria**/
                    if (vm.laudoRelatorioAvaliacao.vistoria.isNaoPossuiVistoria) {
                        // vm.imovel.imagens = vm.listaFotosImovel;
                        vm.imovel.imagens = [];
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoriaAvaliacao) {
                            // benfeitoriaAvaliacao.benfeitoriaImovel.imagens = (vm.listaFotosBenfeitorias[benfeitoriaAvaliacao.id] ? vm.listaFotosBenfeitorias[benfeitoriaAvaliacao.id] : []);
                            benfeitoriaAvaliacao.benfeitoriaImovel.imagens = [];
                        });
                        if (vm.unidadeAutonomaIncorporacao) {
                            // vm.unidadeAutonomaIncorporacao.imagens = vm.listaFotosUnidadeAutonoma;
                            vm.unidadeAutonomaIncorporacao.imagens = [];
                        }
                    } else {
                        vm.imovel.imagens = vm.vistoriaListaFotosImovel;
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoriaAvaliacao) {
                            // benfeitoriaAvaliacao.benfeitoriaImovel.imagens = (vm.vistoriaListaFotosBenfeitorias[benfeitoriaAvaliacao.id] ? vm.vistoriaListaFotosBenfeitorias[benfeitoriaAvaliacao.id] : []);
                            benfeitoriaAvaliacao.benfeitoriaImovel.imagens = [];
                        });
                        if (vm.unidadeAutonomaIncorporacao) {
                            // vm.unidadeAutonomaIncorporacao.imagens = vm.vistoriaListaFotosUnidadeAutonoma;
                            vm.unidadeAutonomaIncorporacao.imagens = [];
                        }
                    }
                    /**Identificacao**/
                    vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos = vm.listaResponsaveisTecnicos;
                    vm.laudoRelatorioAvaliacao.identificacao.listaNormasUtilizadas = vm.listaNormasUtilizadas;
                    /**Detalher Imovel**/
                    // /*vm.imovel.pontoImovel = comumAvaliacaoService.converterJsonToGeo(vm.imovel.pontoImovel);*/
                    vm.imovel.matriculas = vm.listaMatriculasTranscricao;
                    vm.imovel.certidoesCartorial = vm.listaCertidoesCartoriais;
                    vm.laudoRelatorioAvaliacao.detalhesImovel.imovel = vm.imovel;
                    // /**Terreno Imovel**/
                    vm.laudoRelatorioAvaliacao.terrenoImovel.terreno = vm.terreno;
                    vm.laudoRelatorioAvaliacao.terrenoImovel.documentosParametrosUrbanisticos = vm.documentosParametrosUrbanisticos;
                    // /**Benfeitorias**/
                    vm.laudoRelatorioAvaliacao.benfeitorias.benfeitoriasAvaliacao = vm.listaBenfeitoriaAvaliacao;
                    // /**Unidade Autonoma**/
                    if (!(vm.laudoRelatorioAvaliacao.benfeitorias.benfeitoriasAvaliacao.length > 0)) {
                        vm.laudoRelatorioAvaliacao.unidadeAutonoma = vm.unidadeAutonomaIncorporacao;
                    }

                };

                vm.avancarParaProximaAba = function () {
                    vm.navegarEntreAbas(AVANCAR);
                };

                vm.retornarParaAbaAnterior = function () {
                    vm.navegarEntreAbas(RETORNAR);
                };


                vm.navegarEntreAbas = function (processo) {
                    if (vm.tabSelect === vm.IDENTIFICACAO) {
                        if (processo === AVANCAR) {
                            vm.tabSelect = vm.DETALHES_IMOVEL;
                        }
                    } else if (vm.tabSelect === vm.DETALHES_IMOVEL) {
                        if (processo === RETORNAR) {
                            vm.tabSelect = vm.IDENTIFICACAO;
                        } else if (processo === AVANCAR) {
                            vm.tabSelect = vm.TERRENO_IMOVEL;
                        }
                    } else if (vm.tabSelect === vm.TERRENO_IMOVEL) {
                        if (processo === RETORNAR) {
                            vm.tabSelect = vm.DETALHES_IMOVEL;
                        } else if (processo === AVANCAR) {
                            if (vm.listaBenfeitoriaAvaliacao && vm.listaBenfeitoriaAvaliacao.length > 0) {
                                vm.tabSelect = vm.BENFEITORIAS;
                            } else if (vm.unidadeAutonomaIncorporacao) {
                                vm.tabSelect = vm.UNIDADE_AUTONOMA;
                            } else {
                                vm.tabSelect = vm.VISTORIA;
                            }
                        }
                    } else if (vm.tabSelect === vm.BENFEITORIAS) {
                        if (processo === RETORNAR) {
                            vm.tabSelect = vm.TERRENO_IMOVEL;
                        } else if (processo === AVANCAR) {
                            vm.tabSelect = vm.VISTORIA;
                        }
                    } else if (vm.tabSelect === vm.UNIDADE_AUTONOMA) {
                        if (processo === RETORNAR) {
                            vm.tabSelect = vm.TERRENO_IMOVEL;
                        } else if (processo === AVANCAR) {
                            vm.tabSelect = vm.VISTORIA;
                        }
                    } else if (vm.tabSelect === vm.VISTORIA) {
                        if (processo === RETORNAR) {
                            if (vm.listaBenfeitoriaAvaliacao && vm.listaBenfeitoriaAvaliacao.length > 0) {
                                vm.tabSelect = vm.BENFEITORIAS;
                            } else if (vm.unidadeAutonomaIncorporacao) {
                                vm.tabSelect = vm.UNIDADE_AUTONOMA;
                            } else {
                                vm.tabSelect = vm.TERRENO_IMOVEL;
                            }
                        } else if (processo === AVANCAR) {
                            vm.tabSelect = vm.AVALIACAO;
                        }
                    } else if (vm.tabSelect === vm.AVALIACAO) {
                        if (processo === RETORNAR) {
                            vm.tabSelect = vm.VISTORIA;
                        } else if (processo === AVANCAR) {
                            vm.tabSelect = vm.RESULTADO_RELATORIO;
                            vm.tabSelect = vm.RESULTADO;
                        }
                    } else if (vm.tabSelect === vm.RESULTADO) {
                        if (processo === RETORNAR) {
                            vm.tabSelect = vm.AVALIACAO;
                        } else if (processo === AVANCAR) {
                            vm.tabSelect = vm.ANEXOS;
                        }
                    } else if (vm.tabSelect === vm.RESULTADO_RELATORIO) {
                        if (processo === RETORNAR) {
                            vm.tabSelect = vm.AVALIACAO;
                        } else if (processo === AVANCAR) {
                            vm.tabSelect = vm.ANEXOS;
                        }
                    } else if (vm.tabSelect === vm.ANEXOS) {
                        if (processo === RETORNAR) {
                            vm.tabSelect = vm.RESULTADO_RELATORIO;
                            vm.tabSelect = vm.RESULTADO;
                        }
                    }
                    vm.isSalvoRascunho = false;
                };


                vm.redirecionarInformarValorCub = function (benfeitoria, tipoTerreno, avaliacao) {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', tipoTerreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', benfeitoria.benfeitoriaImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.RESULTADO);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultado', vm.formularioResultado);

                    $state.go('avaliacao.INFORMAR_VALOR_CUB_LAUDO_RELATORIO');
                };

                vm.redirecionarInformarValorEdificacao = function (benfeitoria, tipoTerreno, avaliacao) {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', tipoTerreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', benfeitoria.benfeitoriaImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultado', vm.formularioResultado);

                    $state.go('avaliacao.CADASTRAR_VALOR_EDIFICACAO');
                };

                vm.redirecionarSelecionarAmostras = function (avaliacao) {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultado', vm.formularioResultado);

                    $state.go('avaliacao.SELECIONAR_AMOSTRA');
                };

                vm.redirecionarAdicionarVariavelAmostras = function (avaliacao, idAmostra) {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultado', vm.formularioResultado);

                    $state.go('avaliacao.ADICIONAR_VARIAVEL_AMOSTRA', {
                        idAmostra: idAmostra
                    });
                };

                vm.redirecionarCadastrarDocumentoEdificacaoBenfeitoria = function (listaDocumentosEdificacaoBenfeitoria, editar) {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.laudoRelatorioAvaliacao.avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaDocumentosEdificacaoBenfeitoria', listaDocumentosEdificacaoBenfeitoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('editar', editar);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultado', vm.formularioResultado);

                    $state.go('avaliacao.CADASTRAR_DOCUMENTO_EDIFICACAO');
                };

                vm.redirecionarCadastrarDocumentoEdificacaoUnidadeAutonoma = function (listaDocumentosEdificacaoUnidadeAutonoma, editar) {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaDocumentosEdificacaoUnidadeAutonoma', listaDocumentosEdificacaoUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('editar', editar);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultado', vm.formularioResultado);

                    $state.go('avaliacao.CADASTRAR_DOCUMENTO_EDIFICACAO');
                };

                init();

                /*****EDITAR LAUDO RELATORIO AVALIACAO****/


                function buscarDadosEditarLaudoRelatorioAvaliacaoEditar(idLaudo) {
                    laudoRelatorioService.consultarPorId(idLaudo).then(function (resp) {
                        vm.laudoRelatorioAvaliacao = resp.data;
                        vm.imovel = vm.laudoRelatorioAvaliacao.detalhesImovel.imovel;

                        vm.ripImovel = vm.laudoRelatorioAvaliacao.identificacao.rip;
                        vm.dadosEnderecamento = vm.imovel.enderecamento;
                        vm.terreno = vm.laudoRelatorioAvaliacao.terrenoImovel.terreno;

                        vm.listaResponsaveisTecnicos = vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos;
                        vm.listaNormasUtilizadas = vm.laudoRelatorioAvaliacao.identificacao.listaNormasUtilizadas;
                        vm.listaMatriculasTranscricao = vm.imovel.matriculas;

                        if (avaliacaoEscopoCompartilhadoService.getObjeto('listaDocumentosEdificacaoUnidadeAutonoma')) {
                            vm.listaDocumentosEdificacaoUnidadeAutonoma = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('listaDocumentosEdificacaoUnidadeAutonoma'));
                            vm.unidadeAutonoma = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonoma'));
                            vm.unidadeAutonomaIncorporacao = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaIncorporacao'));
                            vm.documentacaoUnidadeAutonoma = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('documentacaoUnidadeAutonoma'));
                            vm.editar = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('editar'));
                            recuperarValoresLaudoRelatorioInformarCub();
                        } else {
                            vm.unidadeAutonomaIncorporacao = vm.laudoRelatorioAvaliacao.unidadeAutonoma;
                        }

                        if (avaliacaoEscopoCompartilhadoService.getObjeto('listaDocumentosEdificacaoBenfeitoria')) {
                            vm.listaDocumentosEdificacaoBenfeitoria = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('listaDocumentosEdificacaoBenfeitoria'));
                            vm.editar = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('editar'));
                            vm.benfeitorias = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('benfeitorias'));
                            vm.listaBenfeitoriaAvaliacao = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriaAvaliacao'));
                            recuperarValoresLaudoRelatorioInformarCub();
                        } else {
                            vm.listaBenfeitoriaAvaliacao = vm.laudoRelatorioAvaliacao.benfeitorias.benfeitoriasAvaliacao;
                        }

                        if (avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao')) {
                            recuperarValoresLaudoRelatorioInformarCub();
                        }

                        buscarImagensImovelEditar(vm.laudoRelatorioAvaliacao.detalhesImovel.imovel.id);
                        buscarImagensBenfeitoriasEditar(vm.laudoRelatorioAvaliacao.benfeitorias.id);
                        buscarImagensUnidadeAutonomaEditar(vm.laudoRelatorioAvaliacao.unidadeAutonoma.id);
                        buscarDocumentosCartoriaisEditar(vm.laudoRelatorioAvaliacao.detalhesImovel.id);
                        buscarDocumentosParametrosUrbanisticosEditar(vm.laudoRelatorioAvaliacao.terrenoImovel.id);
                        buscarAnexosAvaliacaoEditar(vm.laudoRelatorioAvaliacao.avaliacao.id);
                        buscarDadosProprietarioImovelEditar(vm.laudoRelatorioAvaliacao.detalhesImovel.imovel.idOriginal);
                        buscarDadosGeometriaIncorporacaoEditar(vm.laudoRelatorioAvaliacao.identificacao.rip);

                        $timeout(function () {
                            vm.hash = JSON.stringify(vm.laudoRelatorioAvaliacao);
                        }, 15000)
                    });
                };

                var buscarImagensImovelEditar = function (idImovel) {
                    imagemAvaliacaoService.recuperarImagensImovel(idImovel).then(function (resp) {
                        if (vm.laudoRelatorioAvaliacao.vistoria.isNaoPossuiVistoria) {
                            vm.listaFotosImovel = resp.data;
                        } else {
                            vm.vistoriaListaFotosImovel = resp.data;
                        }
                    });
                };
                var buscarImagensBenfeitoriasEditar = function (idBenfeitorias) {
                    imagemAvaliacaoService.recuperarImagensBenfeitorias(idBenfeitorias).then(function (resp) {
                        if (vm.laudoRelatorioAvaliacao.vistoria.isNaoPossuiVistoria) {
                            vm.listaFotosBenfeitorias = resp.data;
                        } else {
                            vm.vistoriaListaFotosBenfeitorias = resp.data;
                        }
                    });
                };

                var buscarImagensUnidadeAutonomaEditar = function (idUnidadeAutonoma) {
                    imagemAvaliacaoService.recuperarImagensUnidadeAutonoma(idUnidadeAutonoma).then(function (resp) {
                        if (vm.laudoRelatorioAvaliacao.vistoria.isNaoPossuiVistoria) {
                            vm.listaFotosUnidadeAutonoma = resp.data;
                        } else {
                            vm.vistoriaListaFotosUnidadeAutonoma = resp.data;
                        }
                    });
                };

                var buscarDocumentosCartoriaisEditar = function (idDetalhesImovel) {
                    laudoRelatorioService.recuperarDocumentosCartoriais(idDetalhesImovel).then(function (resp) {
                        vm.listaCertidoesCartoriais = resp.data;
                        angular.forEach(vm.listaCertidoesCartoriais, function (certidaoCartorial) {
                            certidaoCartorial.arquivo.id = null;
                        });
                    });
                };

                var buscarDocumentosParametrosUrbanisticosEditar = function (idTerrenoImovel) {
                    laudoRelatorioService.recuperarDocumentosParametrosUrbanisticos(idTerrenoImovel).then(function (resp) {
                        vm.documentosParametrosUrbanisticos = resp.data;
                    });
                };

                var buscarAnexosAvaliacaoEditar = function (idAvaliacao) {
                    avaliacaoLaudoService.recuperarAnexosAvaliacao(idAvaliacao).then(function (resp) {
                        vm.listaAnexosAvaliacao = resp.data;
                    });
                };

                var buscarDadosProprietarioImovelEditar = function (idImovel) {
                    imovelAvaliacaoService.buscarDadosDetalharProprietarioLaudo(idImovel).then(function (resp) {
                        vm.dadosProprietario = resp.data;
                    });
                };

                var buscarDadosGeometriaIncorporacaoEditar = function (ripImovel) {
                    imovelAvaliacaoService.buscarDadosGeometriaIncorporacao(ripImovel).then(function (resp) {
                        vm.pontoImovel = resp.data.pontoImovel;
                    });
                };

                vm.limparDadosListaBenfeitorias = function () {
                    if (vm.listaBenfeitoriaAvaliacao && vm.listaBenfeitoriaAvaliacao.length > 0) {
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                            benfeitoria.benfeitoriaImovel.valor = null;
                            benfeitoria.benfeitoriaImovel.padraoAcabamento = null;
                            benfeitoria.benfeitoriaImovel.tipoProjetoCub = null;
                            benfeitoria.benfeitoriaImovel.idadeAparente = null;
                            benfeitoria.benfeitoriaImovel.vidaUtil = null;
                            benfeitoria.benfeitoriaImovel.estadoConservacao = null;
                            benfeitoria.benfeitoriaImovel.valorCub = null;
                            benfeitoria.benfeitoriaImovel.valorCubDepreciado = null;
                            benfeitoria.benfeitoriaImovel.valorEdificacao = null;
                        });
                    } else if (vm.unidadeAutonomaIncorporacao) {
                        vm.unidadeAutonomaIncorporacao.valor = null;
                        vm.unidadeAutonomaIncorporacao.padraoAcabamento = null;
                        vm.unidadeAutonomaIncorporacao.tipoProjetoCub = null;
                    }
                }

                vm.redirecionarEditarLaudoRelatorio = function () {
                    $state.go('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: vm.laudoRelatorioAvaliacao.id
                    });
                };


                vm.redirecionarAssinarLaudoRelatorio = function () {
                    $state.go('avaliacao.VISUALIZAR_LAUDO_RELATORIO_AVALIACAO_PDF', {
                        idLaudo: vm.laudoRelatorioAvaliacao.id
                    });
                };

                vm.verificarAssinaturaResponsavelTecnico = function () {
                    for (var i in vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos) {
                        if (vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos[i]) {
                            if (vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos[i].isAssinado) {
                                return true;
                            }
                        }
                    }
                    return false;
                };


            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('consultarLaudoRelatorioAvaliacaoController',
            ["$scope", "$state", "dominioAvaliacaoService", "identificacaoLaudoRelatorioService", "laudoRelatorioService", "usuarioAvaliacaoService", "ComumFactory", "$filter", "avaliacaoLaudoService", "$mdMedia", "$mdDialog", "imovelAvaliacaoService", function ($scope, $state, dominioAvaliacaoService, identificacaoLaudoRelatorioService, laudoRelatorioService, usuarioAvaliacaoService,
                      ComumFactory, $filter, avaliacaoLaudoService, $mdMedia, $mdDialog, imovelAvaliacaoService) {

                var vm = $scope;

                vm.idBrasil = null;
                vm.filtroConsulta = {};

                vm.listaDadosLaudoRelatorio = {};

                vm.tiposIdentificacao = [];
                vm.metodosAvaliacao = [];
                vm.situacoesLaudoRelatorio = {};
                vm.listaPaises = [];
                vm.listaUFs = [];
                vm.listaMunicipios = [];
                vm.listaTipoImovel = [];
                vm.listaNaturezaImovel = [];
                vm.tiposUnidadeAutonoma = [];
                vm.listaTiposValores = [];
                vm.BRASIL = 'Brasil';

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                vm.UNIAO = 'União';
                vm.AUTARQUIA_FUNDACAO = 'Autarquia/Fundação';
                vm.TERCEIROS = 'Terceiros';

                vm.UNIDADE_AUTONOMA = 'Unidade autônoma';

                vm.validadeLaudo = {
                    VALIDO: 'Válido',
                    VENCIDO: 'Vencido'
                };

                vm.EM_PREENCHIMENTO = 'EM_PREENCHIMENTO';
                vm.AGUARDANDO_ASSINATURA = 'AGUARDANDO_ASSINATURA';
                vm.AGUARDANDO_VINCULACAO = 'AGUARDANDO_VINCULACAO';
                vm.VINCULADO_IMOVEL = 'VINCULADO_IMOVEL';
                vm.CANCELADO = 'CANCELADO';

                var VALOR_MEDIO = 'VALOR_MEDIO';
                var VALOR_MINIMO = 'VALOR_MINIMO';
                var VALOR_MAXIMO = 'VALOR_MAXIMO';
                var OUTRO_VALOR = 'OUTRO_VALOR';

                var init = function () {

                    vm.topDirections = ['left'];
                    vm.bottomDirections = ['down', 'right'];
                    vm.isOpen = false;
                    vm.availableModes = ['md-fling', 'md-scale'];
                    vm.selectedMode = 'md-scale';
                    vm.availableDirections = ['right'];
                    vm.selectedDirection = 'right';

                    buscarUsuarioLogado();
                    vm.buscarLaudosRelatoriosPaginacao();
                    buscarTiposIdentificacao();
                    buscarMetodosAvaliacao();
                    buscarSituacoesLaudo();
                    pesquisarListaTipoImovel();
                    pesquisarListaNaturezaImovel();
                    pesquisarTiposUnidadeAutonoma();
                    pesquisarPaises();
                    pesquisarUfs();
                    buscarTiposValores();
                };

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_CONSULTAR_VISUALIZAR_LAUDORELATORIO === 'false' && vm.usuarioLogado.AVALIACAO_INCLUIR_EDITAR_LAUDORELATORIO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });

                };

                vm.usuarioLogadoHomologador = function (homologadores) {
                    for (var i = 0; i < homologadores.length; i++) {
                        if (homologadores[i].cpf === vm.usuarioLogado.cpf) {
                            if (homologadores[i].isAssinado) {
                                return false;
                            } else {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                vm.verificarAssinaturaResponsavelTecnico = function (homologadores) {
                    for (var i in homologadores) {
                        if (homologadores[i]) {
                            if (homologadores[i].isAssinado) {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                vm.redirecionarCadastrarLaudoRelatorioAvaliacao = function () {
                    $state.go('avaliacao.CADASTRAR_LAUDO_RELATORIO_AVALIACAO');
                };

                vm.redirecionarEditarLaudoRelatorioAvaliacao = function (idLaudo) {
                    $state.go('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: idLaudo
                    });
                };

                vm.limparCamposPesquisa = function () {
                    vm.filtroConsulta = {};
                };

                vm.buscarLaudosRelatoriosPaginacao = function () {
                    laudoRelatorioService.consultaLaudosRelatorioPaginacao(vm.filtroConsulta, (vm.registroInicial - 1), vm.tamanhoLimite)
                        .then(function (resp) {
                            vm.listaDadosLaudoRelatorio = resp.data.content;
                            vm.totalItems = resp.data.totalElements;
                        });
                };

                var buscarTiposIdentificacao = function () {
                    identificacaoLaudoRelatorioService.buscarTiposIdentificacao().then(function (resp) {
                        vm.tiposIdentificacao = resp.data;
                    });
                };

                var buscarMetodosAvaliacao = function () {
                    identificacaoLaudoRelatorioService.buscarMetodosAvaliacao().then(function (resp) {
                        vm.metodosAvaliacao = resp.data;
                    });
                };

                var buscarSituacoesLaudo = function () {
                    laudoRelatorioService.buscarSituacoesLaudo().then(function (resp) {
                        // vm.situacoesLaudoRelatorio = resp.data;

                        for (var situacao in resp.data) {
                            if (situacao != vm.AGUARDANDO_VINCULACAO) {
                                vm.situacoesLaudoRelatorio[situacao] = resp.data[situacao];
                            }
                        }
                    });
                };

                var pesquisarPaises = function () {
                    dominioAvaliacaoService.consultarImovelPaisesSU().then(function (resposta) {
                        vm.listaPaises = resposta.data;

                        angular.forEach(vm.listaPaises, function (pais) {
                            if (pais.descricao == 'Brasil') {
                                vm.idBrasil = pais.id;
                            }
                        });
                    });
                };

                var pesquisarUfs = function () {
                    dominioAvaliacaoService.pesquisarUfsIntegrador().then(function (respostaUf) {
                        vm.listaUFs = respostaUf.data;
                    });
                };

                vm.pesquisarMunicipiosPorUf = function () {
                    vm.listaMunicipios = [];
                    dominioAvaliacaoService.pesquisarMuniciposPorUfIntegrador(vm.filtroConsulta.uf).then(function (resposta) {
                        vm.listaMunicipios = resposta.data;
                    });
                };

                var pesquisarListaTipoImovel = function () {
                    laudoRelatorioService.buscarTiposImovel().then(function (resp) {
                        vm.listaTipoImovel = resp.data;
                    });
                };

                var pesquisarListaNaturezaImovel = function () {
                    laudoRelatorioService.buscarNaturezaImovel().then(function (resp) {
                        vm.listaNaturezaImovel = resp.data;
                    });
                };

                var pesquisarTiposUnidadeAutonoma = function () {
                    laudoRelatorioService.buscarTiposUnidadeAutonoma().then(function (resp) {
                        vm.tiposUnidadeAutonoma = resp.data;
                    });
                };

                var buscarTiposValores = function () {
                    avaliacaoLaudoService.buscarTiposValor().then(function (resp) {
                        vm.listaTiposValores = resp.data;
                    });
                };

                vm.consultarLocalidadeEnderecoCep = function () {
                    if (vm.filtroConsulta.cep && vm.filtroConsulta.cep != '') {
                        dominioAvaliacaoService.pesquisarLocalidadePorCepIntegrador(vm.filtroConsulta.cep)
                            .then(function (resp) {
                                vm.filtroConsulta.uf = resp.data.uf;
                                vm.filtroConsulta.municipio = resp.data.municipio;
                                vm.filtroConsulta.bairro = resp.data.bairro;
                            }, function () {
                                vm.filtroConsulta.uf = null;
                                vm.filtroConsulta.municipio = null;
                                vm.filtroConsulta.bairro = null;

                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-cep-invalido'));
                            });
                    } else {
                        vm.filtroConsulta.uf = null;
                        vm.filtroConsulta.municipio = null;
                        vm.filtroConsulta.bairro = null;
                    }

                };

                vm.validarValorLaudoRelatorio = function (laudoRelatorio) {
                    if (laudoRelatorio.identificacao.metodologia === 'OUTRO') {
                        if (laudoRelatorio.avaliacao.resultadosAvaliacao.valorAdotado) {
                            return $filter('currency')(laudoRelatorio.avaliacao.resultadosAvaliacao.valorAdotado).replace('R$', '');
                        }
                    } else if (laudoRelatorio.identificacao.metodologia === 'GLEBA_URBANIZAVEL') {
                        if (laudoRelatorio.avaliacao.valorTotalGleba) {
                            return $filter('currency')(laudoRelatorio.avaliacao.valorTotalGleba).replace('R$', '');
                        }
                    } else if (laudoRelatorio.identificacao.metodologia === 'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA' || laudoRelatorio.identificacao.metodoAvaliacao === 'EVOLUTIVO') {
                        if (laudoRelatorio.resultadoAvaliacao.valorImovel) {
                            return $filter('currency')(laudoRelatorio.resultadoAvaliacao.valorImovel).replace('R$', '');
                        }
                    } else if (laudoRelatorio.avaliacao.resultadosAvaliacao && laudoRelatorio.avaliacao.resultadosAvaliacao.tipoValor) {
                        if (laudoRelatorio.avaliacao.resultadosAvaliacao.tipoValor === VALOR_MEDIO) {
                            if (laudoRelatorio.avaliacao.resultadosAvaliacao.valorMedio) {
                                return $filter('currency')(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMedio).replace('R$', '');
                            }

                        } else if (laudoRelatorio.avaliacao.resultadosAvaliacao.tipoValor === VALOR_MINIMO) {
                            if (laudoRelatorio.avaliacao.resultadosAvaliacao.valorMinimo) {
                                return $filter('currency')(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMinimo).replace('R$', '');
                            }

                        } else if (laudoRelatorio.avaliacao.resultadosAvaliacao.tipoValor === VALOR_MAXIMO) {
                            if (laudoRelatorio.avaliacao.resultadosAvaliacao.valorMaximo) {
                                return $filter('currency')(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMaximo).replace('R$', '');
                            }

                        } else if (laudoRelatorio.avaliacao.resultadosAvaliacao.tipoValor === OUTRO_VALOR) {
                            if (laudoRelatorio.avaliacao.resultadosAvaliacao.outroValorAdotado) {
                                return $filter('currency')(laudoRelatorio.avaliacao.resultadosAvaliacao.outroValorAdotado).replace('R$', '');
                            }
                        }
                    }

                    return '-';

                };

                vm.redirecionarAssinarLaudoRelatorio = function (idLaudo) {
                    $state.go('avaliacao.ASSINAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: idLaudo
                    });
                };

                vm.redirecionarDetalharLaudoRelatorio = function (idLaudo) {
                    $state.go('avaliacao.DETALHAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: idLaudo
                    });
                };

                vm.redirecionarEditarLaudoRelatorio = function (idLaudo) {
                    $state.go('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: idLaudo
                    });
                };

                vm.abrirDialogMapaDetalharImovel = function (ev, ripImovel) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    imovelAvaliacaoService.buscarDadosDetalharImovelLaudoRelatorioRip(ripImovel).then(function (response) {
                        $mdDialog.show({
                            controller: ["$scope", function ($scope) {
                                var vm = $scope;
                                vm.pontoImovel = response.data.pontoImovel;
                                vm.geometriaTerreno = response.data.terrenoImovel.geometriaTerreno;
                                vm.benfeitorias = response.data.benfeitorias;
                                vm.dadosImovelAvaliacao = response.data;

                                vm.fecharDialogMapaDetalharImovel = function () {
                                    $mdDialog.cancel();
                                };
                            }],
                            templateUrl: 'scripts-avaliacao/app/directives/partial/dialogMapaDetalharImovel.tmpl.html',
                            parent: angular.element(document.body),
                            targetEvent: ev,
                            clickOutsideToClose: true,
                            fullscreen: useFullScreen
                        });
                    });
                };

                vm.removerLaudoRelatorio = function (idLaudo) {
                    var confirm = $mdDialog.confirm()
                        .title('Atenção!')
                        .textContent('Tem certeza que deseja excluir este Laudo/Relatório?')
                        .ok('Confirmar')
                        .cancel('Cancelar');

                    $mdDialog.show(confirm).then(function () {
                        laudoRelatorioService.deletarLaudoRelatorio(idLaudo).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-rascunho-removido'));
                            vm.buscarLaudosRelatoriosPaginacao();
                        });
                    }, function () {

                    });
                };

                init();
            }]);

})();
(function () {
    'use strict';

    angular.module('su-avaliacao').controller('cadastrarLaudoRelatorioAvaliacaoController', ["$scope", "laudoRelatorioService", "$stateParams", "imovelAvaliacaoService", "$state", "$filter", "ComumFactory", "dominioAvaliacaoService", "benfeitoriasLaudoRelatorioService", "unidadeAutonomaAvaliacaoService", "validacaoLaudoRelatorioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "$mdDialog", "usuarioAvaliacaoService", "imagemAvaliacaoService", "avaliacaoLaudoService", "$timeout", "laudoAvaliacaoService", function ($scope, laudoRelatorioService, $stateParams,
                                                                                                      imovelAvaliacaoService, $state, $filter,
                                                                                                      ComumFactory, dominioAvaliacaoService,
                                                                                                      benfeitoriasLaudoRelatorioService, unidadeAutonomaAvaliacaoService,
                                                                                                      validacaoLaudoRelatorioAvaliacaoService,
                                                                                                      avaliacaoEscopoCompartilhadoService, $mdDialog, usuarioAvaliacaoService,
                                                                                                      imagemAvaliacaoService, avaliacaoLaudoService, $timeout, laudoAvaliacaoService) {
        var vm = $scope;

        vm.IDENTIFICACAO = 'IDENTIFICACAO';
        vm.DETALHES_IMOVEL = 'DETALHES_IMOVEL';
        vm.TERRENO_IMOVEL = 'TERRENO_IMOVEL';
        vm.BENFEITORIAS = 'BENFEITORIAS';
        vm.UNIDADE_AUTONOMA = 'UNIDADE_AUTONOMA';
        vm.UTILIZACAO = 'UTILIZACAO';
        vm.VISTORIA = 'VISTORIA';
        vm.AVALIACAO = 'AVALIACAO';
        vm.RESULTADO = 'RESULTADO';
        vm.RESULTADO_RELATORIO = 'RESULTADO_RELATORIO';
        vm.ANEXOS = 'ANEXOS';
        vm.QUANTIFICACAO = 'QUANTIFICACAO';
        vm.RESULTADO_AVALIACAO = 'RESULTADO_AVALIACAO';
        vm.CANCELAMENTO = 'CANCELAMENTO';

        vm.carregaVistoria = false;
        vm.carregaIdentificacao = false;
        vm.detalharLaudoRelatorio = false;

        var RETORNAR = 'RETORNAR';
        var AVANCAR = 'AVANCAR';

        vm.RETORNAR = 'RETORNAR';
        vm.AVANCAR = 'AVANCAR';

        vm.HECTARE = 'HECTARE';

        vm.formularioIdentificacao = null;
        vm.formularioDetalhesImovel = null;
        vm.formularioTerrenoImovel = null;
        vm.formularioBenfeitorias = null;
        vm.formularioUnidadeAutonoma = null;
        vm.formularioUtilizacao = null;
        vm.formularioVistoria = null;
        vm.formularioAvaliacao = null;
        vm.formularioResultadosLaudo = null;
        vm.formularioResultadosRelatorio = null;
        vm.formularioQuantificacao = null;
        vm.formularioQuantificacaoOrcamento = null;
        vm.formularioResultadoAvaliacao = null;
        vm.formularioAnexo = null;

        vm.ripImovel = null;
        vm.imovel = {};
        vm.listaResponsaveisTecnicos = [];
        vm.listaNormasUtilizadas = [];
        vm.listaMatriculasTranscricao = [];
        vm.listaCertidoesCartoriais = [];
        vm.documentosParametrosUrbanisticos = [];
        vm.listaBenfeitoriaAvaliacao = [];
        vm.terreno = {};
        vm.unidadeAutonomaIncorporacao = null;
        vm.listaFotosImovel = [];
        vm.listaFotosBenfeitorias = [];
        vm.listaFotosUnidadeAutonoma = [];

        vm.vistoriaListaFotosImovel = [];
        vm.vistoriaListaFotosBenfeitorias = [];
        vm.vistoriaListaFotosUnidadeAutonoma = [];
        vm.mostrarResultado = false;

        vm.pontoImovel = null;
        vm.dadosEnderecamento = {};
        vm.dadosProprietario = {};

        vm.CONTENT_TYPE_02 = 'CONTENT_TYPE_02';

        vm.isSalvoRascunho = false;

        var init = function () {
            buscarUsuarioLogado(function () {
                if ($state.current.name.indexOf('ASSINAR_LAUDO_RELATORIO_AVALIACAO') !== -1 || $state.current.name.indexOf('DETALHAR_LAUDO_RELATORIO_AVALIACAO') !== -1) {
                    vm.detalharLaudoRelatorio = true;
                } else {
                    if (vm.usuarioLogado.AVALIACAO_INCLUIR_EDITAR_LAUDORELATORIO === 'false') {
                        $state.go('avaliacao.ACESSO_NEGADO');
                    }
                }

            });
            if (avaliacaoEscopoCompartilhadoService.getObjeto('isRipBloqueado')) {
                vm.isRipBloqueado = true;
                avaliacaoEscopoCompartilhadoService.limparEscopo();
            } else {
                vm.isRipBloqueado = false;
            }

            if (avaliacaoEscopoCompartilhadoService.getObjeto('editar')) {
                vm.tabSelect = vm.BENFEITORIAS;
            } else if (!avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect')) {
                vm.tabSelect = vm.IDENTIFICACAO;
            }

            // vm.tabSelect = vm.AVALIACAO;

            vm.desabilitarAba = true;
            inicializarLaudoRelatorio(null);

            if ($stateParams.idLaudo) {
                vm.carregaVistoria = true;
                vm.desabilitarAba = false;
                buscarDadosEditarLaudoRelatorioAvaliacaoEditar($stateParams.idLaudo);
            } else if (avaliacaoEscopoCompartilhadoService.getObjeto('ripImovel') && avaliacaoEscopoCompartilhadoService.getObjeto('laudoRelatorioAvaliacao')) {
                recuperarValoresLaudoRelatorioInformarCub();
                buscarListaBenfeitoriasSu($stateParams.rip);
                vm.carregaIdentificacao = true;
            } else if ($stateParams.rip) {
                vm.ripImovel = $stateParams.rip;
                vm.buscarDadosImovelPorRip($stateParams.rip);
                vm.carregaIdentificacao = true;
            } else {
                vm.carregaIdentificacao = true;
            }

        };

        var buscarUsuarioLogado = function (callback) {
            usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                vm.usuarioLogado = resp.data;
                if (callback) {
                    callback();
                }
            });
        };

        vm.buscarDadosImovelPorRip = function (ripImovel) {
            var idsBenfeitorias = [];
            imovelAvaliacaoService.buscarDadosDetalharImovelLaudoRelatorioRip(ripImovel).then(function (resposta) {
                if (resposta.data && resposta.data !== '' && (vm.usuarioLogado.JURISDICOES.indexOf(resposta.data.enderecamento.uf) !== -1)) {
                    inicializarLaudoRelatorio(ripImovel);
                    vm.imovel = resposta.data;
                    vm.imovel.id = null;
                    vm.pontoImovel = vm.imovel.pontoImovel;
                    vm.ripImovel = ripImovel;
                    vm.dadosEnderecamento = vm.imovel.enderecamento;
                    vm.dadosProprietario = vm.imovel.proprietario;
                    vm.listaMatriculasTranscricao = vm.imovel.matriculas;
                    vm.listaBenfeitoriaAvaliacao = vm.imovel.benfeitorias;
                    vm.unidadeAutonomaIncorporacao = vm.imovel.unidadeAutonoma;
                    vm.laudoRelatorioAvaliacao.terrenoImovel.terreno.areaTotal = vm.imovel.terrenoImovel.areaTotalCadastral;

                    for (var i = 0; i < vm.imovel.certidoesCartorial.length; i++) {
                        if (vm.imovel.certidoesCartorial[i].arquivo && !vm.imovel.certidoesCartorial[i].arquivo.nome && !vm.imovel.certidoesCartorial[i].arquivo.contentType && !vm.imovel.certidoesCartorial[i].nome && !vm.imovel.certidoesCartorial[i].idCartorio) {
                            vm.imovel.certidoesCartorial.splice(i, i);
                        } else {
                            vm.listaCertidoesCartoriais.push(vm.imovel.certidoesCartorial[i]);
                        }
                    }

                    var benfeitoriaAnexos = [];

                    angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                        benfeitoria.benfeitoriaImovel = angular.copy(benfeitoria);
                        idsBenfeitorias.push(benfeitoria.id);
                        benfeitoria.benfeitoriaImovel.idOriginal = angular.copy(benfeitoria.id);
                        benfeitoria.id = null;

                        if (benfeitoria.benfeitoriaImovel.edificacao) {
                            if (benfeitoria.benfeitoriaImovel.edificacao.documentacao) {
                                for (var j = 0; j < benfeitoria.benfeitoriaImovel.edificacao.documentacao.length; j++) {
                                    benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].possui = benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].possui === null ? 'SEM_INFORMACAO' : benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].possui;
                                }
                            }

                            //Adiciona documentos de benfeitorias
                            if (benfeitoria.tipoBenfeitoria.descricao === 'Edificação') {
                                var codigoBenfeitoria = benfeitoria.benfeitoriaImovel.codigo;
                                codigoBenfeitoria += ' - ';
                                codigoBenfeitoria += benfeitoria.benfeitoriaImovel.edificacao.tipoProjeto.descricao;
                                codigoBenfeitoria += '/';
                                codigoBenfeitoria += benfeitoria.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao;

                                var benfeitoriaAnexo = new InformacaoAnexo('Documentação de Edificação da Benfeitoria ' + codigoBenfeitoria, 0);
                                benfeitoriaAnexo.filhos = [];

                                if (benfeitoria.benfeitoriaImovel.edificacao.documentacao) {
                                    for (var j = 0; j < benfeitoria.benfeitoriaImovel.edificacao.documentacao.length; j++) {
                                        benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].id = null;
                                        if (benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].arquivo) {
                                            benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].arquivo.id = null;
                                        }

                                        benfeitoriaAnexo.filhos.push({
                                            nome: benfeitoria.benfeitoriaImovel.edificacao.documentacao[j].nome,
                                            selecionado: false,
                                            ordem: j,
                                            documentoEdificacao: benfeitoria.benfeitoriaImovel.edificacao.documentacao[j]
                                        });
                                    }
                                }

                                benfeitoriaAnexos.push(benfeitoriaAnexo);

                                var benfeitoriaAnexoValorEdificacao = new InformacaoAnexo('Documentação de Valor Edificação da Benfeitoria ' + codigoBenfeitoria, 0);
                                benfeitoriaAnexoValorEdificacao.filhos = [];

                                benfeitoriaAnexos.push(benfeitoriaAnexoValorEdificacao);
                            }
                        }

                    });

                    //Inclui os documentos no array de anexo
                    if (benfeitoriaAnexos.length) {
                        var laudoRelatorioAnexo = angular.copy(vm.laudoRelatorioAvaliacao.anexo.anexos);
                        vm.laudoRelatorioAvaliacao.anexo.anexos = [];

                        for (var i = 0; i < laudoRelatorioAnexo.length; i++) {

                            if (laudoRelatorioAnexo[i].nome === 'Documentação de Edificação da Unidade Autônoma') {
                                vm.laudoRelatorioAvaliacao.anexo.anexos = vm.laudoRelatorioAvaliacao.anexo.anexos.concat(benfeitoriaAnexos);
                            } else {
                                vm.laudoRelatorioAvaliacao.anexo.anexos.push(laudoRelatorioAnexo[i]);
                            }

                        }

                    }

                    if (vm.unidadeAutonomaIncorporacao) {
                        vm.unidadeAutonomaIncorporacao.idOriginal = angular.copy(vm.unidadeAutonomaIncorporacao.id);
                        vm.unidadeAutonomaIncorporacao.id = null;

                        if (vm.laudoRelatorioAvaliacao.anexo && vm.laudoRelatorioAvaliacao.anexo.anexos) {

                            //Adicionar os documentos da unidade autonoma
                            for (var i = 0; i < vm.laudoRelatorioAvaliacao.anexo.anexos.length; i++) {
                                if (vm.laudoRelatorioAvaliacao.anexo.anexos[i].nome === 'Documentação de Edificação da Unidade Autônoma') {
                                    if (!vm.laudoRelatorioAvaliacao.anexo.anexos[i].filhos || !vm.laudoRelatorioAvaliacao.anexo.anexos[i].filhos.length) {
                                        vm.laudoRelatorioAvaliacao.anexo.anexos[i].filhos = [];
                                    }

                                    for (var j = 0; j < vm.unidadeAutonomaIncorporacao.edificacao.documentacao.length; j++) {
                                        vm.unidadeAutonomaIncorporacao.edificacao.documentacao[j].id = null;
                                        if (vm.unidadeAutonomaIncorporacao.edificacao.documentacao[j].arquivo) {
                                            vm.unidadeAutonomaIncorporacao.edificacao.documentacao[j].arquivo.id = null;
                                        }

                                        vm.laudoRelatorioAvaliacao.anexo.anexos[i].filhos.push({
                                            nome: vm.unidadeAutonomaIncorporacao.edificacao.documentacao[j].nome,
                                            selecionado: false,
                                            ordem: vm.laudoRelatorioAvaliacao.anexo.anexos[i].filhos.length,
                                            documentoEdificacao: vm.unidadeAutonomaIncorporacao.edificacao.documentacao[j]
                                        });
                                    }
                                }
                            }

                        }
                    }
                    buscarImagensImovelPorRip(ripImovel);
                    buscarImagensBenfeitorias(idsBenfeitorias);
                    buscarImagensUnidadeAutonoma(vm.unidadeAutonomaIncorporacao);
                    buscarListaBenfeitoriasSu(ripImovel);
                    getUnidade();

                    vm.carregaVistoria = true;

                } else {
                    vm.ripImovel = null;
                    vm.laudoRelatorioAvaliacao.identificacao.rip = null;
                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-rip-invalido'));
                }
            }, function () {
                vm.ripImovel = null;
                vm.laudoRelatorioAvaliacao.identificacao.rip = null;
                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-rip-invalido'));
            });
        };

        var buscarListaBenfeitoriasSu = function (rip) {
            imovelAvaliacaoService.buscarBenfeitoriasSuPorRip(rip).then(function (resp) {
                vm.benfeitoriasTemp = resp.data;

                for (var i = 0; i < vm.benfeitoriasTemp.length; i++) {
                    if (!vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.edificacao.documentacao) {
                        vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.edificacao.documentacao = [];
                        for (var j = 0; j < vm.benfeitoriasTemp[i].edificacao.documentacao.length; j++) {
                            if (vm.benfeitoriasTemp[i].edificacao.documentacao[j].arquivo) {
                                vm.benfeitoriasTemp[i].edificacao.documentacao[j].arquivo.id = null;
                            }
                            vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.edificacao.documentacao.push({
                                nome: vm.benfeitoriasTemp[i].edificacao.documentacao[j].nome,
                                dtEmissao: vm.benfeitoriasTemp[i].edificacao.documentacao[j].dataEmissao,
                                dtValidade: vm.benfeitoriasTemp[i].edificacao.documentacao[j].dataValidade,
                                arquivo: vm.benfeitoriasTemp[i].edificacao.documentacao[j].arquivo,
                                isPossui: vm.benfeitoriasTemp[i].edificacao.documentacao[j].possui === null ? null : vm.benfeitoriasTemp[i].edificacao.documentacao[j].possui.name
                            });
                        }
                    } else {
                        // for (j = 0; j < vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.edificacao.documentacao.length; j++) {
                        //     vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.edificacao.documentacao[j].id = null;
                        //     if (vm.benfeitoriasTemp[i].edificacao.documentacao[j].arquivo) {
                        //         vm.benfeitoriasTemp[i].edificacao.documentacao[j].arquivo = null;
                        //     }
                        // }
                    }
                }

            });
        };

        var buscarImagensImovelPorRip = function (ripImovel) {
            imovelAvaliacaoService.buscarFotosImovel(ripImovel).then(function (resp) {
                vm.listaFotosImovel = resp.data;
            });
        };

        var buscarImagensBenfeitorias = function (idsBenfeitorias) {
            if (idsBenfeitorias && idsBenfeitorias.length > 0) {
                benfeitoriasLaudoRelatorioService.buscarFotosBenfeitorias(idsBenfeitorias.toString())
                    .then(function (imagens) {
                        vm.listaFotosBenfeitorias = imagens.data;
                    });
            }
        };

        var buscarImagensUnidadeAutonoma = function (unidadeAutonoma) {
            if (unidadeAutonoma) {
                unidadeAutonomaAvaliacaoService.buscarImagensUnidadeAutonoma(unidadeAutonoma.idOriginal)
                    .then(function (resp) {
                        vm.listaFotosUnidadeAutonoma = resp.data;
                    });
            }
        };

        var inicializarLaudoRelatorio = function (ripImovel) {
            vm.laudoRelatorioAvaliacao = {
                identificacao: {
                    rip: ripImovel
                },
                detalhesImovel: {
                    imovel: {}
                },
                terrenoImovel: {
                    terreno: {},
                    documentosParametrosUrbanisticos: []
                },
                benfeitorias: {
                    benfeitoriasAvaliacao: []
                },
                unidadeAutonoma: {
                    unidadeAutonomaIncorporacao: {}
                },
                utilizacao: {},
                vistoria: {
                    dataVistoria: ''
                },
                avaliacao: {
                    enquadramento: {
                        isAdocaoEstimativaTendencia: true
                    }
                },
                anexo: {
                    anexos: [
                        new InformacaoAnexo('Fotos da Vistoria', 0),
                        new InformacaoAnexo('Dados de Mercado - Fotos', 1),
                        new InformacaoAnexo('Documento Cartorial', -1),
                        new InformacaoAnexo('Documento Parâmetros Urbanísticos', -1),
                        new InformacaoAnexo('Documentação de Edificação da Unidade Autônoma', -1),
                        new InformacaoAnexo('Relatórios Estatísticos', -1),
                        new InformacaoAnexo('Arquivos de Orçamento', -1),
                        new InformacaoAnexo('Orçamento/Fonte de Custos', -1),
                        new InformacaoAnexo('Orçamento/Fonte', -1),
                        new InformacaoAnexo('Fonte BDI', -1),
                        new InformacaoAnexo('Fonte Depreciação', -1),
                        new InformacaoAnexo('Avaliação - Anexos', -1),
                        new InformacaoAnexo('Fonte Fator de Comercialização', -1),
                        new InformacaoAnexo('Anexos', -1),
                        new InformacaoAnexo('Memorial Descritivo do Imóvel', -1)
                    ],
                    documentosComplementares: []
                }
            };
        };

        var recuperarValoresLaudoRelatorioInformarCub = function () {
            vm.laudoRelatorioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoRelatorioAvaliacao');
            vm.dadosEnderecamento = avaliacaoEscopoCompartilhadoService.getObjeto('dadosEnderecamento');
            vm.dadosProprietario = avaliacaoEscopoCompartilhadoService.getObjeto('dadosProprietario');

            /**Identificacao**/
            vm.ripImovel = avaliacaoEscopoCompartilhadoService.getObjeto('ripImovel');
            vm.pontoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('pontoImovel');
            vm.listaResponsaveisTecnicos = avaliacaoEscopoCompartilhadoService.getObjeto('listaResponsaveisTecnicos');
            vm.listaNormasUtilizadas = avaliacaoEscopoCompartilhadoService.getObjeto('listaNormasUtilizadas');
            /**Detalher Imovel**/
            vm.listaMatriculasTranscricao = avaliacaoEscopoCompartilhadoService.getObjeto('listaMatriculasTranscricao');
            vm.listaCertidoesCartoriais = avaliacaoEscopoCompartilhadoService.getObjeto('listaCertidoesCartoriais');

            vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
            vm.listaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosImovel');
            /**Terreno Imovel**/
            vm.terreno = avaliacaoEscopoCompartilhadoService.getObjeto('terreno');
            vm.documentosParametrosUrbanisticos = avaliacaoEscopoCompartilhadoService.getObjeto('documentosParametrosUrbanisticos');
            /**Benfeitorias**/
            vm.listaBenfeitoriaAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriaAvaliacao');
            vm.listaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosBenfeitorias');
            /**Unidade Autonoma**/
            vm.unidadeAutonomaIncorporacao = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaIncorporacao');
            vm.listaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosUnidadeAutonoma');
            /**Vistoria**/
            vm.vistoriaListaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosImovel');
            vm.vistoriaListaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosBenfeitorias');
            vm.vistoriaListaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosUnidadeAutonoma');
            /**Avaliacao**/
            vm.tabSelect = avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect');
            vm.laudoRelatorioAvaliacao.avaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao');
            /**Anexos**/
            if (avaliacaoEscopoCompartilhadoService.getObjeto('anexoLaudoRelatorio')) {
                vm.laudoRelatorioAvaliacao.anexo = avaliacaoEscopoCompartilhadoService.getObjeto('anexoLaudoRelatorio');
            }

            vm.formularioIdentificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioIdentificacao');
            vm.formularioDetalhesImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioDetalhesImovel');
            vm.formularioTerrenoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioTerrenoImovel');
            vm.formularioBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('formularioBenfeitorias');
            vm.formularioUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUnidadeAutonoma');
            vm.formularioUtilizacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUtilizacao');
            vm.formularioVistoria = avaliacaoEscopoCompartilhadoService.getObjeto('formularioVistoria');
            vm.formularioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAvaliacao');
            vm.formularioResultadosRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosRelatorio');
            vm.formularioResultadosLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosLaudo');
            vm.formularioQuantificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacao');
            vm.formularioQuantificacaoOrcamento = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacaoOrcamento');
            vm.formularioResultadoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadoAvaliacao');
            vm.formularioAnexo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAnexo');

            avaliacaoEscopoCompartilhadoService.limparEscopo();
        };

        vm.redirecionarConsultarImovelLaudoRelatorio = function () {
            if (vm.detalharLaudoRelatorio) {
                $state.go('avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO');
            } else {
                var confirm = $mdDialog.confirm({focusOnOpen: false})
                    .title($filter('translate')('label-titulo-confirmacao'))
                    .textContent($filter('translate')('mensagem-dados-nao-serao-salvos-confirm'))
                    .ok($filter('translate')('btn-sim'))
                    .cancel($filter('translate')('btn-nao'));
                $mdDialog.show(confirm).then(function () {
                    $state.go('avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO');
                }, function () {
                    $mdDialog.cancel();
                });
            }
        };

        var validarCreaResponsaveisTecnicosInformados = function () {
            var creaPendente = false;
            if (vm.listaResponsaveisTecnicos.length === 0) {
                creaPendente = true;
                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-necessario-informar-crea-responsavel'));
            }
            angular.forEach(vm.listaResponsaveisTecnicos, function (responsavelTecnico) {
                if (!responsavelTecnico.crea) {
                    creaPendente = true;
                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-necessario-informar-crea-responsavel'));
                }
            });

            return creaPendente;
        };

        var preencherDadosAbasLaudoRelatorio = function () {
            /**Vistoria**/
            if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_NAO_REALIZADA') {
                vm.imovel.imagens = vm.listaFotosImovel;
                // vm.imovel.imagens = [];
                angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoriaAvaliacao) {
                    benfeitoriaAvaliacao.benfeitoriaImovel.imagens = (vm.listaFotosBenfeitorias[benfeitoriaAvaliacao.id] ? vm.listaFotosBenfeitorias[benfeitoriaAvaliacao.id] : []);
                    // benfeitoriaAvaliacao.benfeitoriaImovel.imagens = [];
                });
                if (vm.unidadeAutonomaIncorporacao) {
                    vm.unidadeAutonomaIncorporacao.imagens = vm.listaFotosUnidadeAutonoma;
                    // vm.unidadeAutonomaIncorporacao.imagens = [];
                }
            } else if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_REALIZADA_EM_UM_DIA') {
                vm.imovel.imagens = vm.vistoriaListaFotosImovel;
                angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoriaAvaliacao) {
                    benfeitoriaAvaliacao.benfeitoriaImovel.imagens = (vm.vistoriaListaFotosBenfeitorias[benfeitoriaAvaliacao.id] ? vm.vistoriaListaFotosBenfeitorias[benfeitoriaAvaliacao.id] : []);
                    // benfeitoriaAvaliacao.benfeitoriaImovel.imagens = [];
                });
                if (vm.unidadeAutonomaIncorporacao) {
                    vm.unidadeAutonomaIncorporacao.imagens = vm.vistoriaListaFotosUnidadeAutonoma;
                    // vm.unidadeAutonomaIncorporacao.imagens = [];
                }
            }
            /**Identificacao**/
            vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos = vm.listaResponsaveisTecnicos;
            vm.laudoRelatorioAvaliacao.identificacao.listaNormasUtilizadas = vm.listaNormasUtilizadas;
            /**Detalher Imovel**/
            // /*vm.imovel.pontoImovel = comumAvaliacaoService.converterJsonToGeo(vm.imovel.pontoImovel);*/
            vm.imovel.matriculas = vm.listaMatriculasTranscricao;
            vm.imovel.certidoesCartorial = vm.listaCertidoesCartoriais;
            vm.laudoRelatorioAvaliacao.detalhesImovel.imovel = vm.imovel;
            // /**Terreno Imovel**/
            vm.laudoRelatorioAvaliacao.terrenoImovel.terreno = vm.terreno;
            vm.laudoRelatorioAvaliacao.terrenoImovel.documentosParametrosUrbanisticos = vm.documentosParametrosUrbanisticos;
            // /**Benfeitorias**/
            vm.laudoRelatorioAvaliacao.benfeitorias.benfeitoriasAvaliacao = vm.listaBenfeitoriaAvaliacao;
            // /**Unidade Autonoma**/
            if (vm.laudoRelatorioAvaliacao.unidadeAutonoma !== null) {
                vm.laudoRelatorioAvaliacao.unidadeAutonoma.unidadeAutonomaIncorporacao = vm.unidadeAutonomaIncorporacao;
            }

        };

        vm.avancarParaProximaAba = function () {
            if (vm.tabSelect === vm.IDENTIFICACAO && (validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioIdentificacao) || validarCreaResponsaveisTecnicosInformados())) {
                return;
            } else {
                vm.desabilitarAba = false;
                verificaDados(function () {
                    vm.navegarEntreAbas(AVANCAR);
                });
            }

        };

        vm.retornarParaAbaAnterior = function () {
            verificaDados(function () {
                vm.navegarEntreAbas(RETORNAR);
            });
        };

        vm.selecionarAba = function (tabSelect) {
            if (vm.showOnSelectAba) {
                verificaDados(function () {
                    vm.tabSelect = tabSelect;
                });
            } else {
                vm.showOnSelectAba = true;
            }
        };

        var verificaDados = function (callback) {
            if (vm.hash !== JSON.stringify(vm.laudoRelatorioAvaliacao)) {
                var confirm = $mdDialog.confirm({focusOnOpen: false})
                    .title($filter('translate')('label-titulo-confirmacao'))
                    .textContent($filter('translate')('mensagem-deseja-gravar-dados-rascunho-confirm'))
                    .ok($filter('translate')('btn-sim'))
                    .cancel($filter('translate')('btn-nao'));
                $mdDialog.show(confirm).then(function () {
                    vm.showOnSelectAba = false;
                    vm.salvarComoRascunhoLaudoRelatorio(null, callback);
                }, function () {
                    vm.showOnSelectAba = false;
                    callback();
                });
            } else {
                callback();
            }
        };

        vm.confirmarSalvarComoRascunhoLaudoRelatorioParaAdicionarVariavel = function () {
            var confirm = $mdDialog.confirm({focusOnOpen: false})
                .title($filter('translate')('label-titulo-confirmacao'))
                .textContent($filter('translate')('mensagem-deseja-gravar-dados-rascunho-confirm-adicionar-variavel'))
                .ok($filter('translate')('btn-sim'))
                .cancel($filter('translate')('btn-nao'));
            $mdDialog.show(confirm).then(function () {
                vm.salvarComoRascunhoLaudoRelatorio();
            }, function () {
            });
        };

        vm.navegarEntreAbas = function (processo) {
            if (vm.tabSelect === vm.IDENTIFICACAO) {
                if (processo === AVANCAR) {
                    vm.tabSelect = vm.DETALHES_IMOVEL;
                }
            } else if (vm.tabSelect === vm.DETALHES_IMOVEL) {
                if (processo === RETORNAR) {
                    vm.tabSelect = vm.IDENTIFICACAO;
                } else if (processo === AVANCAR) {
                    vm.tabSelect = vm.TERRENO_IMOVEL;
                }
            } else if (vm.tabSelect === vm.TERRENO_IMOVEL) {
                if (processo === RETORNAR) {
                    vm.tabSelect = vm.DETALHES_IMOVEL;
                } else if (processo === AVANCAR) {
                    if (vm.listaBenfeitoriaAvaliacao && vm.listaBenfeitoriaAvaliacao.length > 0) {
                        vm.tabSelect = vm.BENFEITORIAS;
                    } else if (vm.unidadeAutonomaIncorporacao) {
                        vm.tabSelect = vm.UNIDADE_AUTONOMA;
                    } else {
                        vm.tabSelect = vm.VISTORIA;
                    }
                }
            } else if (vm.tabSelect === vm.BENFEITORIAS) {
                if (processo === RETORNAR) {
                    vm.tabSelect = vm.TERRENO_IMOVEL;
                } else if (processo === AVANCAR) {
                    vm.tabSelect = vm.VISTORIA;
                }
            } else if (vm.tabSelect === vm.UNIDADE_AUTONOMA) {
                if (processo === RETORNAR) {
                    vm.tabSelect = vm.TERRENO_IMOVEL;
                } else if (processo === AVANCAR) {
                    vm.tabSelect = vm.VISTORIA;
                }
            } else if (vm.tabSelect === vm.VISTORIA) {
                if (processo === RETORNAR) {
                    if (vm.listaBenfeitoriaAvaliacao && vm.listaBenfeitoriaAvaliacao.length > 0) {
                        vm.tabSelect = vm.BENFEITORIAS;
                    } else if (vm.unidadeAutonomaIncorporacao) {
                        vm.tabSelect = vm.UNIDADE_AUTONOMA;
                    } else {
                        vm.tabSelect = vm.TERRENO_IMOVEL;
                    }
                } else if (processo === AVANCAR) {
                    vm.tabSelect = vm.AVALIACAO;
                }
            } else if (vm.tabSelect === vm.AVALIACAO) {
                if (processo === RETORNAR) {
                    vm.tabSelect = vm.VISTORIA;
                } else if (processo === AVANCAR) {
                    if (vm.laudoRelatorioAvaliacao.identificacao.metodologia === 'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA') {
                        vm.tabSelect = vm.RESULTADO_AVALIACAO;
                    } else if (vm.laudoRelatorioAvaliacao.identificacao.metodologia === 'GLEBA_URBANIZAVEL') {
                        vm.tabSelect = vm.ANEXOS;
                    } else {
                        vm.tabSelect = vm.RESULTADO;
                    }
                }
            } else if (vm.tabSelect === vm.RESULTADO) {
                if (processo === RETORNAR) {
                    vm.tabSelect = vm.AVALIACAO;
                } else if (processo === AVANCAR) {
                    if (vm.laudoRelatorioAvaliacao.identificacao.tipo === 'LAUDO' && vm.laudoRelatorioAvaliacao.identificacao.metodoAvaliacao === 'EVOLUTIVO') {
                        vm.tabSelect = vm.QUANTIFICACAO;
                    } else {
                        vm.tabSelect = vm.ANEXOS;
                    }
                }
            } else if (vm.tabSelect === vm.QUANTIFICACAO) {
                if (processo === RETORNAR) {
                    vm.tabSelect = vm.RESULTADO;
                } else if (processo === AVANCAR) {
                    vm.tabSelect = vm.RESULTADO_AVALIACAO;
                    vm.determinarValorAdotadoSelecionado();
                }
            } else if (vm.tabSelect === vm.RESULTADO_AVALIACAO) {
                if (processo === RETORNAR) {
                    if (vm.laudoRelatorioAvaliacao.identificacao.tipo === 'LAUDO' && vm.laudoRelatorioAvaliacao.identificacao.metodoAvaliacao === 'EVOLUTIVO') {
                        vm.tabSelect = vm.QUANTIFICACAO;
                    } else {
                        vm.tabSelect = vm.AVALIACAO;
                    }

                } else if (processo === AVANCAR) {
                    vm.tabSelect = vm.ANEXOS;
                }
            } else if (vm.tabSelect === vm.ANEXOS) {
                if (processo === RETORNAR) {
                    if (vm.laudoRelatorioAvaliacao.identificacao.tipo === 'LAUDO' && vm.laudoRelatorioAvaliacao.identificacao.metodoAvaliacao === 'EVOLUTIVO') {
                        vm.tabSelect = vm.RESULTADO_AVALIACAO;
                    } else if (vm.laudoRelatorioAvaliacao.identificacao.metodologia === 'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA') {
                        vm.tabSelect = vm.RESULTADO_AVALIACAO;
                    } else if (vm.laudoRelatorioAvaliacao.identificacao.metodologia === 'GLEBA_URBANIZAVEL') {
                        vm.tabSelect = vm.AVALIACAO;
                    } else {
                        vm.tabSelect = vm.RESULTADO;
                    }
                }
            }
            vm.isSalvoRascunho = false;
        };

        vm.salvarComoRascunhoLaudoRelatorio = function (tipoNavegacao, callback) {

            if (vm.laudoRelatorioAvaliacao.id === null) {
                if (vm.laudoRelatorioAvaliacao.identificacao.tipo === 'LAUDO' && vm.laudoRelatorioAvaliacao.identificacao.metodoAvaliacao === 'EVOLUTIVO') {
                    vm.validarMetodoAvaliacao = false;
                    if (vm.imovel.unidadeAutonoma != null) {
                        vm.validarMetodoAvaliacao = true;
                    } else {
                        if (vm.imovel.benfeitorias != null) {
                            for (var i = 0; i < vm.imovel.benfeitorias.length; i++) {
                                if (vm.imovel.benfeitorias[i].tipoBenfeitoria.descricao === 'Edificação') {
                                    vm.validarMetodoAvaliacao = true;
                                    break;
                                }
                            }
                        }
                    }
                    if (vm.validarMetodoAvaliacao === false) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-metodo-avaliacao-invalido'));
                        return;
                    }
                }
            } else {
                if (vm.laudoRelatorioAvaliacao.identificacao.tipo === 'LAUDO' && vm.laudoRelatorioAvaliacao.identificacao.metodoAvaliacao === 'EVOLUTIVO') {
                    vm.validarMetodoAvaliacao = false;

                    if (vm.listaBenfeitoriaAvaliacao != null) {
                        for (i = 0; i < vm.listaBenfeitoriaAvaliacao.length; i++) {
                            if (vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.tipoBenfeitoria.descricao === 'Edificação') {
                                vm.validarMetodoAvaliacao = true;
                                break;
                            }
                        }
                    }

                    if (vm.validarMetodoAvaliacao === false) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-metodo-avaliacao-invalido'));
                        return;
                    }
                }
            }

            if (vm.tabSelect === vm.IDENTIFICACAO
                && (validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioIdentificacao)
                    || validarCreaResponsaveisTecnicosInformados())) {
                return;
            }
            vm.desabilitarAba = false;
            preencherDadosAbasLaudoRelatorio();

            laudoRelatorioService.salvarRascunho(vm.laudoRelatorioAvaliacao).then(function (resp) {
             
                iniciaDatasVistoria(resp.data.vistoria);

                vm.laudoRelatorioAvaliacao = angular.copy(resp.data);
                preencherCamposAbasLaudoRelatorioSalvo();

                $timeout(function () {
                    vm.laudoRelatorioAvaliacao.identificacao = angular.copy(resp.data.identificacao)
                });

                $timeout(function () {
                    vm.hash = JSON.stringify(vm.laudoRelatorioAvaliacao);
                }, 8000);
                vm.isSalvoRascunho = true;
                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-salvos'));

                if (tipoNavegacao) {
                    vm.navegarEntreAbas(tipoNavegacao);
                }

                if (callback) {
                    callback();
                }
            });
        };

        var preencherCamposAbasLaudoRelatorioSalvo = function () {
            /**Identificacao**/
            vm.ripImovel = vm.laudoRelatorioAvaliacao.identificacao.rip;
            vm.listaResponsaveisTecnicos = vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos;
            vm.listaNormasUtilizadas = vm.laudoRelatorioAvaliacao.identificacao.listaNormasUtilizadas;
            /**Detalher Imovel**/
            vm.listaMatriculasTranscricao = vm.imovel.matriculas;
            vm.listaCertidoesCartoriais = vm.imovel.certidoesCartorial;
            vm.imovel = vm.laudoRelatorioAvaliacao.detalhesImovel.imovel;
            /**Terreno Imovel**/
            vm.terreno = vm.laudoRelatorioAvaliacao.terrenoImovel.terreno;
            vm.documentosParametrosUrbanisticos = vm.laudoRelatorioAvaliacao.terrenoImovel.documentosParametrosUrbanisticos;
            /**Benfeitorias**/
            vm.listaBenfeitoriaAvaliacao = vm.laudoRelatorioAvaliacao.benfeitorias.benfeitoriasAvaliacao;
            /**Unidade Autonoma**/
            if (vm.laudoRelatorioAvaliacao.unidadeAutonoma) {
                vm.unidadeAutonomaIncorporacao = vm.laudoRelatorioAvaliacao.unidadeAutonoma.unidadeAutonomaIncorporacao;
            }

            /**Vistoria**/
            if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_NAO_REALIZADA') {
                vm.listaFotosImovel = vm.imovel.imagens;
                vm.listaFotosBenfeitorias = [];
                angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoriaAvaliacao) {
                    var idBenfeitoria = benfeitoriaAvaliacao.benfeitoriaImovel.id;
                    vm.listaFotosBenfeitorias[idBenfeitoria] = benfeitoriaAvaliacao.benfeitoriaImovel.imagens;
                });
                if (vm.unidadeAutonomaIncorporacao) {
                    vm.listaFotosUnidadeAutonoma = vm.unidadeAutonomaIncorporacao.imagens;
                }
            } else if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_REALIZADA_EM_UM_DIA') {
                vm.vistoriaListaFotosImovel = vm.imovel.imagens;
                vm.vistoriaListaFotosBenfeitorias = [];
                angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoriaAvaliacao) {
                    var idBenfeitoria = benfeitoriaAvaliacao.benfeitoriaImovel.id;
                    vm.vistoriaListaFotosBenfeitorias[idBenfeitoria] = benfeitoriaAvaliacao.benfeitoriaImovel.imagens;
                });
                if (vm.unidadeAutonomaIncorporacao) {
                    vm.vistoriaListaFotosUnidadeAutonoma = vm.unidadeAutonomaIncorporacao.imagens;
                }
            }
        };

        vm.concluirCadastroLaudoRelatorioAvaliacao = function (validarAssinar) {

            preencherDadosAbasLaudoRelatorio();

            if (validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioIdentificacao)) {
                vm.tabSelect = vm.IDENTIFICACAO;
                return;
            }
            if (validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioDetalhesImovel)) {
                vm.tabSelect = vm.DETALHES_IMOVEL;
                return;
            }
            if (validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioTerrenoImovel)) {
                vm.tabSelect = vm.TERRENO_IMOVEL;
                return;
            }
            if (vm.formularioBenfeitorias && validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioBenfeitorias)) {
                vm.tabSelect = vm.BENFEITORIAS;
                return;
            }
            if (vm.formularioUnidadeAutonoma && validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioUnidadeAutonoma)) {
                vm.tabSelect = vm.UNIDADE_AUTONOMA;
                return;
            }

            if (validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioVistoria)) {
                vm.tabSelect = vm.VISTORIA;
                return;
            } else {
                if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_REALIZADA_EM_UM_DIA') {
                    if (vm.vistoriaListaFotosImovel.length === 0 && vm.vistoriaListaFotosBenfeitorias.length === 0 && vm.vistoriaListaFotosUnidadeAutonoma.length === 0) {
                        vm.tabSelect = vm.VISTORIA;
                        return;
                    }
                }
            }
            if (validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioAvaliacao)) {
                vm.tabSelect = vm.AVALIACAO;
                return;
            }
            if (vm.laudoRelatorioAvaliacao.identificacao.tipo === 'LAUDO') {
                if (vm.formularioResultadosLaudo && validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioResultadosLaudo)) {
                    vm.tabSelect = vm.RESULTADO;
                    return;
                }
            }
            if (vm.laudoRelatorioAvaliacao.identificacao.tipo === 'RELATORIO') {
                if (vm.formularioResultadosRelatorio && validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioResultadosRelatorio)) {
                    vm.tabSelect = vm.RESULTADO;
                    return;
                }
            }
            if (vm.laudoRelatorioAvaliacao.identificacao.metodoAvaliacao === 'EVOLUTIVO') {
                if (vm.laudoRelatorioAvaliacao.identificacao.quantificacaoCustoBenfeitoria === 'ORCAMENTO') {
                    if (vm.formularioQuantificacaoOrcamento && validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioQuantificacaoOrcamento)) {
                        vm.tabSelect = vm.QUANTIFICACAO;
                        return;
                    }
                } else {
                    if (vm.formularioQuantificacao && validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioQuantificacao)) {
                        vm.tabSelect = vm.QUANTIFICACAO;
                        return;
                    }
                }
            }
            if (vm.laudoRelatorioAvaliacao.identificacao.metodologia === 'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA' || vm.laudoRelatorioAvaliacao.identificacao.metodoAvaliacao === 'EVOLUTIVO') {
                if (vm.formularioResultadoAvaliacao && validacaoLaudoRelatorioAvaliacaoService.validandoFormulario(vm.formularioResultadoAvaliacao)) {
                    vm.tabSelect = vm.RESULTADO_AVALIACAO;
                    return;
                }
            }

            laudoRelatorioService.concluirCadastro(vm.laudoRelatorioAvaliacao)
                .then(function (resp) {
                    vm.laudoRelatorioAvaliacao = resp.data;
                    ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-salvos'));
                    if (!validarAssinar) {
                        $state.go('avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO');
                    } else {
                        $state.go('avaliacao.VISUALIZAR_LAUDO_RELATORIO_AVALIACAO_PDF', {idLaudo: vm.laudoRelatorioAvaliacao.id});
                    }
                });
        };

        vm.redirecionarInformarValorCub = function (benfeitoria, tipoTerreno, tipoValorLaudo) {
            avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
            /**Identificacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
            /**Detalher Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
            avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
            /**Terreno Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
            avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
            /**Benfeitorias**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
            /**Unidade Autonoma**/
            avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
            /**Vistoria**/
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
            /**Avaliacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.laudoRelatorioAvaliacao.avaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', tipoTerreno);
            avaliacaoEscopoCompartilhadoService.setObjetos('tipoValorLaudo', tipoValorLaudo);
            avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', benfeitoria.benfeitoriaImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.RESULTADO);

            avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

            $state.go('avaliacao.INFORMAR_VALOR_CUB_LAUDO_RELATORIO');
        };

        vm.redirecionarInformarValorEdificacao = function (benfeitoria, tipoTerreno, avaliacao) {
            avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
            /**Identificacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
            /**Detalher Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
            avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
            /**Terreno Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
            avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
            /**Benfeitorias**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
            /**Unidade Autonoma**/
            avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
            /**Vistoria**/
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
            /**Avaliacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.laudoRelatorioAvaliacao.avaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', tipoTerreno);
            avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', benfeitoria.benfeitoriaImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.AVALIACAO);
            /**Anexos**/
            avaliacaoEscopoCompartilhadoService.setObjetos('anexoLaudoRelatorio', vm.laudoRelatorioAvaliacao.anexo);

            avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

            $state.go('avaliacao.CADASTRAR_VALOR_EDIFICACAO');
        };

        vm.redirecionarCalcularAreaEquivalente = function (benfeitoria, detalhar, tabSelect, tipoTerreno) {
            avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
            /**Identificacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
            /**Detalher Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
            avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
            /**Terreno Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
            avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
            /**Benfeitorias**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
            /**Unidade Autonoma**/
            avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
            /**Vistoria**/
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
            /**Avaliacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.laudoRelatorioAvaliacao.avaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', tipoTerreno);
            /**Quantificacao custo**/
            avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoriaQuantificacao', benfeitoria);
            avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', tabSelect);
            avaliacaoEscopoCompartilhadoService.setObjetos('detalhar', detalhar);
            avaliacaoEscopoCompartilhadoService.setObjetos('uf', vm.imovel.enderecamento.uf);

            avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

            $state.go('avaliacao.CALCULAR_AREA_EQUIVALENTE');
        };

        vm.redirecionarSelecionarAmostras = function (avaliacao) {
            avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
            /**Identificacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
            /**Detalher Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
            avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
            /**Terreno Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
            avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
            /**Benfeitorias**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
            /**Unidade Autonoma**/
            avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
            /**Vistoria**/
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
            /**Avaliacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', avaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

            avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

            $state.go('avaliacao.SELECIONAR_AMOSTRA');
        };

        vm.redirecionarAdicionarVariavelAmostras = function (avaliacao, variaveisAmostras, idAmostra) {
            avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
            /**Identificacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
            /**Detalher Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
            avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
            /**Terreno Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
            avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
            /**Benfeitorias**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
            /**Unidade Autonoma**/
            avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
            /**Vistoria**/
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
            /**Avaliacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', avaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);
            avaliacaoEscopoCompartilhadoService.setObjetos('variaveisAmostras', variaveisAmostras);

            avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

            if (idAmostra) {
                $state.go('avaliacao.ADICIONAR_VARIAVEL_AMOSTRA', {
                    idAmostra: idAmostra
                });
            } else {
                $state.go('avaliacao.INSERIR_AMOSTRA_VARIAVEL_AMOSTRA');
            }
        };

        vm.redirecionarCadastrarDocumentoEdificacaoBenfeitoria = function (listaDocumentosEdificacaoBenfeitoria, editar) {
            avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
            /**Identificacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
            /**Detalher Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
            avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
            /**Terreno Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
            avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
            /**Benfeitorias**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
            /**Unidade Autonoma**/
            avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
            /**Vistoria**/
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
            /**Avaliacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.laudoRelatorioAvaliacao.avaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaDocumentosEdificacaoBenfeitoria', listaDocumentosEdificacaoBenfeitoria);
            avaliacaoEscopoCompartilhadoService.setObjetos('editar', editar);
            avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

            avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

            $state.go('avaliacao.CADASTRAR_DOCUMENTO_EDIFICACAO');
        };

        vm.redirecionarCadastrarDocumentoEdificacaoUnidadeAutonoma = function (listaDocumentosEdificacaoUnidadeAutonoma, editar) {
            avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
            /**Identificacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
            /**Detalher Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
            avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
            /**Terreno Imovel**/
            avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
            avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
            /**Benfeitorias**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
            /**Unidade Autonoma**/
            avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
            /**Vistoria**/
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
            /**Avaliacao**/
            avaliacaoEscopoCompartilhadoService.setObjetos('listaDocumentosEdificacaoUnidadeAutonoma', listaDocumentosEdificacaoUnidadeAutonoma);
            avaliacaoEscopoCompartilhadoService.setObjetos('editar', editar);
            avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

            avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
            avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

            $state.go('avaliacao.CADASTRAR_DOCUMENTO_EDIFICACAO');
        };

        init();

        /*****EDITAR LAUDO RELATORIO AVALIACAO****/

        function buscarDadosEditarLaudoRelatorioAvaliacaoEditar(idLaudo) {
            laudoRelatorioService.consultarPorId(idLaudo).then(function (resp) {

                vm.laudoRelatorioAvaliacao = angular.copy(resp.data);
                iniciaDatasVistoria(vm.laudoRelatorioAvaliacao.vistoria);

                vm.imovel = vm.laudoRelatorioAvaliacao.detalhesImovel.imovel;

                vm.ripImovel = vm.laudoRelatorioAvaliacao.identificacao.rip;
                vm.dadosEnderecamento = vm.imovel.enderecamento;
                vm.terreno = vm.laudoRelatorioAvaliacao.terrenoImovel.terreno;

                vm.listaResponsaveisTecnicos = vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos;
                vm.listaNormasUtilizadas = vm.laudoRelatorioAvaliacao.identificacao.listaNormasUtilizadas;
                vm.listaMatriculasTranscricao = vm.imovel.matriculas;

                if ((vm.tabSelect === vm.AVALIACAO || vm.tabSelect === vm.QUANTIFICACAO) && avaliacaoEscopoCompartilhadoService.getObjeto('benfeitoriaQuantificacao')) {
                    vm.benfeitoriaTemp = avaliacaoEscopoCompartilhadoService.getObjeto('benfeitoriaQuantificacao');
                    angular.forEach(vm.laudoRelatorioAvaliacao.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        if (benfeitoria.codigo === vm.benfeitoriaTemp.codigo && benfeitoria.idOriginal === vm.benfeitoriaTemp.idOriginal) {
                            benfeitoria = vm.benfeitoriaTemp;
                        }
                    });
                    recuperarValoresLaudoRelatorioInformarCub();
                }

                if (avaliacaoEscopoCompartilhadoService.getObjeto('listaDocumentosEdificacaoUnidadeAutonoma')) {
                    vm.listaDocumentosEdificacaoUnidadeAutonoma = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('listaDocumentosEdificacaoUnidadeAutonoma'));
                    vm.unidadeAutonoma = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonoma'));
                    vm.unidadeAutonomaIncorporacao = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaIncorporacao'));
                    vm.documentacaoUnidadeAutonoma = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('documentacaoUnidadeAutonoma'));
                    vm.editar = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('editar'));
                    recuperarValoresLaudoRelatorioInformarCub();
                } else {
                    if (vm.laudoRelatorioAvaliacao.unidadeAutonoma) {
                        vm.unidadeAutonomaIncorporacao = vm.laudoRelatorioAvaliacao.unidadeAutonoma.unidadeAutonomaIncorporacao;
                    }
                }

                if (avaliacaoEscopoCompartilhadoService.getObjeto('listaDocumentosEdificacaoBenfeitoria')) {
                    vm.listaDocumentosEdificacaoBenfeitoria = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('listaDocumentosEdificacaoBenfeitoria'));
                    vm.editar = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('editar'));
                    vm.benfeitorias = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('benfeitorias'));
                    vm.listaBenfeitoriaAvaliacao = angular.copy(avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriaAvaliacao'));
                    recuperarValoresLaudoRelatorioInformarCub();
                } else {
                    if (!avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao')) {
                        vm.listaBenfeitoriaAvaliacao = vm.laudoRelatorioAvaliacao.benfeitorias.benfeitoriasAvaliacao;
                    }
                }

                if (avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao')) {
                    recuperarValoresLaudoRelatorioInformarCub();
                }

                buscarImagensImovelEditar(vm.laudoRelatorioAvaliacao.detalhesImovel.imovel.id);
                buscarImagensBenfeitoriasEditar(vm.laudoRelatorioAvaliacao.benfeitorias.id);
                if (vm.laudoRelatorioAvaliacao.unidadeAutonoma) {
                    buscarImagensUnidadeAutonomaEditar(vm.laudoRelatorioAvaliacao.unidadeAutonoma.id);
                }

                buscarDocumentosCartoriaisEditar(vm.laudoRelatorioAvaliacao.detalhesImovel.id);
                buscarDocumentosParametrosUrbanisticosEditar(vm.laudoRelatorioAvaliacao.terrenoImovel.id);
                buscarAnexosAvaliacaoEditar(vm.laudoRelatorioAvaliacao.avaliacao.id);
                if (vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao && vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.id) {
                    buscarRelatoriosEstatisticos(vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.id);
                }
                buscarDocumentosPressupostos(vm.laudoRelatorioAvaliacao.avaliacao.id);
                buscarDadosProprietarioImovelEditar(vm.laudoRelatorioAvaliacao.detalhesImovel.imovel.idOriginal);
                buscarDadosGeometriaIncorporacaoEditar(vm.laudoRelatorioAvaliacao.identificacao.rip);

                $timeout(function () {
                    vm.laudoRelatorioAvaliacao.identificacao = resp.data.identificacao
                });

                $timeout(function () {
                    vm.hash = JSON.stringify(vm.laudoRelatorioAvaliacao);
                }, 15000);
            });
        }

        function iniciaDatasVistoria(vistoria) {
            if (vistoria.dataVistoria) {
                vistoria.dataVistoria = new Date(vistoria.dataVistoria);
            } else {
                vistoria.dataVistoria = undefined;
            }
        }

        var buscarImagensImovelEditar = function (idImovel) {
            imagemAvaliacaoService.recuperarImagensImovel(idImovel).then(function (resp) {
                if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_NAO_REALIZADA') {
                    vm.listaFotosImovel = resp.data;
                } else if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_REALIZADA_EM_UM_DIA') {
                    vm.vistoriaListaFotosImovel = resp.data;
                }
            });
        };
        var buscarImagensBenfeitoriasEditar = function (idBenfeitorias) {
            imagemAvaliacaoService.recuperarImagensBenfeitorias(idBenfeitorias).then(function (resp) {
                if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_NAO_REALIZADA') {
                    vm.listaFotosBenfeitorias = resp.data;
                } else if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_REALIZADA_EM_UM_DIA') {
                    vm.vistoriaListaFotosBenfeitorias = resp.data;
                }
            });
        };

        var buscarImagensUnidadeAutonomaEditar = function (idUnidadeAutonoma) {
            imagemAvaliacaoService.recuperarImagensUnidadeAutonoma(idUnidadeAutonoma).then(function (resp) {
                if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_NAO_REALIZADA') {
                    vm.listaFotosUnidadeAutonoma = resp.data;
                } else if (vm.laudoRelatorioAvaliacao.vistoria.execucaoVistoria === 'VISTORIA_REALIZADA_EM_UM_DIA') {
                    vm.vistoriaListaFotosUnidadeAutonoma = resp.data;
                }
            });
        };

        var buscarDocumentosCartoriaisEditar = function (idDetalhesImovel) {
            laudoRelatorioService.recuperarDocumentosCartoriais(idDetalhesImovel).then(function (resp) {
                vm.listaCertidoesCartoriais = resp.data;
            });
        };

        var buscarDocumentosParametrosUrbanisticosEditar = function (idTerrenoImovel) {
            laudoRelatorioService.recuperarDocumentosParametrosUrbanisticos(idTerrenoImovel).then(function (resp) {
                vm.documentosParametrosUrbanisticos = resp.data;
            });
        };

        var buscarAnexosAvaliacaoEditar = function (idAvaliacao) {
            avaliacaoLaudoService.recuperarAnexosAvaliacao(idAvaliacao).then(function (resp) {
                vm.listaAnexosAvaliacao = resp.data;
            });
        };

        var buscarRelatoriosEstatisticos = function (idResultadosAvaliacao) {
            vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.relatoriosEstatisticos = {};
            avaliacaoLaudoService.recuperarRelatoriosEstatisticos(idResultadosAvaliacao).then(function (resp) {
                vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.relatoriosEstatisticos = resp.data;
            });
        };

        var buscarDocumentosPressupostos = function (idAvaliacao) {
            avaliacaoLaudoService.buscarDocumentosPressupostos(idAvaliacao).then(function (resp) {
                vm.laudoRelatorioAvaliacao.avaliacao.documentosPressupostos = resp.data;
            });
        };

        var buscarDadosProprietarioImovelEditar = function (idImovel) {
            imovelAvaliacaoService.buscarDadosDetalharProprietarioLaudo(idImovel).then(function (resp) {
                vm.dadosProprietario = resp.data;
            });
        };

        var buscarDadosGeometriaIncorporacaoEditar = function (ripImovel) {
            imovelAvaliacaoService.buscarDadosGeometriaIncorporacao(ripImovel).then(function (resp) {
                vm.pontoImovel = resp.data.pontoImovel;
            });
        };

        vm.limparDadosListaBenfeitorias = function () {
            if (vm.listaBenfeitoriaAvaliacao && vm.listaBenfeitoriaAvaliacao.length > 0) {
                angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                    benfeitoria.benfeitoriaImovel.valor = null;
                    benfeitoria.benfeitoriaImovel.padraoAcabamento = null;
                    benfeitoria.benfeitoriaImovel.tipoProjetoCub = null;
                    benfeitoria.benfeitoriaImovel.idadeAparente = null;
                    benfeitoria.benfeitoriaImovel.vidaUtil = null;
                    benfeitoria.benfeitoriaImovel.estadoConservacao = null;
                    benfeitoria.benfeitoriaImovel.valorCub = null;
                    benfeitoria.benfeitoriaImovel.valorCubDepreciado = null;
                    benfeitoria.benfeitoriaImovel.valorEdificacao = null;
                });
            } else if (vm.unidadeAutonomaIncorporacao) {
                vm.unidadeAutonomaIncorporacao.valor = null;
                vm.unidadeAutonomaIncorporacao.padraoAcabamento = null;
                vm.unidadeAutonomaIncorporacao.tipoProjetoCub = null;
            }
        };

        vm.limparDadosQuantificacaoCusto = function () {
            if (vm.laudoRelatorioAvaliacao.identificacao.metodoAvaliacao && vm.laudoRelatorioAvaliacao.identificacao.metodoAvaliacao !== 'EVOLUTIVO') {
                vm.laudoRelatorioAvaliacao.quantificacaoCusto = null;
            }
        };

        vm.redirecionarEditarLaudoRelatorio = function () {
            $state.go('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                idLaudo: vm.laudoRelatorioAvaliacao.id
            });
        };

        vm.redirecionarAssinarLaudoRelatorio = function () {
            $state.go('avaliacao.VISUALIZAR_LAUDO_RELATORIO_AVALIACAO_PDF', {
                idLaudo: vm.laudoRelatorioAvaliacao.id
            });
        };

        vm.verificarAssinaturaResponsavelTecnico = function () {
            for (var i in vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos) {
                if (vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos[i]) {
                    if (vm.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos[i].isAssinado) {
                        return true;
                    }
                }
            }
            return false;
        };

        var getUnidade = function () {
            laudoAvaliacaoService.getOrganizacao().then(function (response) {
                vm.laudoRelatorioAvaliacao.identificacao.unidade = response.data.organizacao;
            });
        };

        vm.determinarValorAdotadoSelecionado = function () {
            if (vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MEDIO) {
                vm.valorAdotadoSelecionado = vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.valorMedio;
            } else if (vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MINIMO) {
                vm.valorAdotadoSelecionado = vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.valorMinimo;
            } else if (vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MAXIMO) {
                vm.valorAdotadoSelecionado = vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.valorMaximo;
            } else if (vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor === vm.OUTRO_VALOR) {
                vm.valorAdotadoSelecionado = vm.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.outroValorAdotado;
            }
        };

        function InformacaoAnexo(nome, ordem) {
            this.id = null;
            this.nome = nome;
            this.descricao = null;
            this.selecionado = false;
            this.ordem = ordem;
            this.filhos = [];
        }

        vm.verificarResponsaveisTecnicos = function () {
            for (var i in vm.listaResponsaveisTecnicos) {
                if (vm.listaResponsaveisTecnicos[i]) {
                    if (vm.listaResponsaveisTecnicos[i].cpf === vm.usuarioLogado.cpf) {
                        return true;
                    }
                }
            }
            return false;
        };
    }]);

})();

(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory('laudoAvaliacaoService', ["$http", "ComumFactory", "$filter", function ($http, ComumFactory, $filter) {

            var URL_LAUDO_AVALIACAO = '/avaliacao/api/laudo-avaliacao';

            var buscarTiposAvaliacaoLaudo = function () {
                return $http.get(URL_LAUDO_AVALIACAO + '/tipoAvaliacao/find/all');
            };

            var buscarMotivosCancelamento = function () {
                return $http.get(URL_LAUDO_AVALIACAO + '/motivosCancelamento/find/all');
            };

            var buscarSituacoesLaudo = function () {
                return $http.get(URL_LAUDO_AVALIACAO + '/situacoesLaudo/find/all');
            };

            var buscarTiposElaboracaoLaudo = function () {
                return $http.get(URL_LAUDO_AVALIACAO + '/tipoElaboracaoLaudo/find/all');
            };

            var buscarFonteValorTerreno = function () {
                return $http.get(URL_LAUDO_AVALIACAO + '/fonteValorTerreno/find/all');
            };

            var buscarTipoNotaTecnicaHomologacao = function () {
                return $http.get(URL_LAUDO_AVALIACAO + '/tipoNotaTecnicaHomologacao/find/all');
            };

            var buscarTipoNotaTecnicaRevalidacaoLaudo = function() {
                var data = this.buscarTipoNotaTecnicaHomologacao().then(function (resp) {

                    return Object.keys(resp.data)
                    .filter(function (key) {
                        return key !== "PREENCHER_SISTEMA";
                    })
                    .reduce(function (obj, key) {
                        obj[key] = resp.data[key];
                        return obj;
                    }, {});
                });

                return data;
            }

            var buscarLaudosRelatorioAvaliacao = function (tipoAvaliacao, situacao, rip, offset, limit) {
                return $http.get(URL_LAUDO_AVALIACAO, {
                    params: {
                        tipoAvaliacao: tipoAvaliacao,
                        situacao: situacao,
                        rip: rip,
                        offset: offset,
                        limit: limit
                    }
                });
            };

            var buscarLaudoContabil = function (rip) {
                return $http.get(URL_LAUDO_AVALIACAO + '/contabil/'+ rip);
            };

            var salvar = function (laudoAvaliacao) {
                return $http.post(URL_LAUDO_AVALIACAO, laudoAvaliacao);
            };

            var consultarLaudoAvaliacaoId = function (idLaudo) {
                return $http.get(URL_LAUDO_AVALIACAO + '/consultar/' + idLaudo);
            };

            var revalidarLaudoAvaliacao = function (idLaudoAvaliacao) {
                return $http.put(URL_LAUDO_AVALIACAO + '/revalidar/' + idLaudoAvaliacao);
            };

            var cancelarLaudoAvaliacao = function (laudoAvaliacao) {
                return $http.put(URL_LAUDO_AVALIACAO + '/cancelar/laudo/relatorio', laudoAvaliacao);
            };

            var verificarCancelarLaudoAvaliacao = function (ripImovel) {
                return $http.get(URL_LAUDO_AVALIACAO + '/verificar/cancelar/laudo/relatorio/' + ripImovel);
            };

            var validarDataAssinaturaCadastrada = function (tipoAvaliacao, dataAssinatura, ripImovel, idLaudo) {
                return $http.get(URL_LAUDO_AVALIACAO + '/validar/data-assinatura', {
                    params: {
                        dataAssinatura: dataAssinatura,
                        ripImovel: ripImovel,
                        tipoAvaliacao: tipoAvaliacao,
                        idLaudo: idLaudo || null
                    }
                });
            };

            var verificarLaudoCadastrado = function (tipoAvaliacao, ripImovel, dataAssinatura, idLaudo) {
                return $http.get(URL_LAUDO_AVALIACAO + '/verificar/laudo-cadastrado', {
                    params: {
                        tipoAvaliacao: tipoAvaliacao,
                        ripImovel: ripImovel,
                        dataAssinatura: dataAssinatura,
                        idLaudo: idLaudo || null
                    }
                });
            };

            var validarValorImovel = function (laudoAvaliacao) {
                    if (!laudoAvaliacao.valorImovel) {
                        laudoAvaliacao.valorImovel = 0;
                    }

                    definirValorTerrenoSemEdificacoes(laudoAvaliacao);

                    if (laudoAvaliacao.valorTerreno > laudoAvaliacao.valorImovel || laudoAvaliacao.valorTerreno < 0) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                        for (i = 0; i < laudoAvaliacao.benfeitoriasImovel.length; i++) {
                            laudoAvaliacao.benfeitoriasImovel[i].valor = 0;
                            laudoAvaliacao.benfeitoriasImovel[i].valorCub = 0;
                        }
                        return;
                    }

                    if (laudoAvaliacao.benfeitoriasImovel && laudoAvaliacao.benfeitoriasImovel.length > 0) {
                        var valorDisponivel = laudoAvaliacao.valorImovel;
                        var erro = false;
                        var temBenfeitoria = false;
                        for (i = 0; i < laudoAvaliacao.benfeitoriasImovel.length; i++) {
                            if (laudoAvaliacao.benfeitoriasImovel[i].valor) {
                                temBenfeitoria = true
                            }
                            valorDisponivel -= laudoAvaliacao.benfeitoriasImovel[i].valor ? laudoAvaliacao.benfeitoriasImovel[i].valor : 0;
                            if(valorDisponivel < 0){
                                erro = true;
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                                laudoAvaliacao.benfeitoriasImovel[i].valor = 0;
                                laudoAvaliacao.benfeitoriasImovel[i].valorCub = 0;
                                return;
                            }
                        }
                        if (!erro && temBenfeitoria && valorDisponivel != laudoAvaliacao.valorTerreno) {
                            if (laudoAvaliacao.termoResponsabilidade) {
                                laudoAvaliacao.termoResponsabilidade.isAssinado = false;
                            }
                            laudoAvaliacao.valorTerreno = valorDisponivel;
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel-diferente'));
                        }
                    }


                };

            var definirValorTerrenoSemEdificacoes = function (laudoAvaliacao) {
                var semBenfeitorias = !laudoAvaliacao.benfeitoriasImovel || laudoAvaliacao.benfeitoriasImovel.length === 0;
                var naoEhUnidadeAutonoma = !laudoAvaliacao.unidadeAutonomaIncorporacao || laudoAvaliacao.unidadeAutonomaIncorporacao.length === 0;

                if (laudoAvaliacao.valorImovel && semBenfeitorias && naoEhUnidadeAutonoma) {
                    laudoAvaliacao.valorTerreno = laudoAvaliacao.valorImovel;
                }
            };

            var getUltimaRevalidacao = function (laudoAvaliacao) {
                if (laudoAvaliacao.revalidacoes != null && laudoAvaliacao.revalidacoes.length > 0) {
                    return laudoAvaliacao.revalidacoes[laudoAvaliacao.revalidacoes.length - 1];
                }
                return null;
            };

            var assinarLaudoAvaliacaoNotaTecnica = function (idLaudo) {
                return $http.put(URL_LAUDO_AVALIACAO + '/assinar/notaTecnica/' + idLaudo);
            };

            var recusarLaudoAvaliacaoNotaTecnica = function (idLaudo) {
                return $http.delete(URL_LAUDO_AVALIACAO + '/recusar/notaTecnica/' + idLaudo);
            };

            var revalidarLaudoAvaliacaoRevalidacao = function (idLaudo, revalidacao) {
                return $http.put(URL_LAUDO_AVALIACAO + '/revalidacao/' + idLaudo, revalidacao);
            };

            var salvarComoRascunhoRevalidacao = function (idLaudo, revalidacao) {
                return $http.put(URL_LAUDO_AVALIACAO + '/revalidacao/salvarRascunho/' + idLaudo, revalidacao);
            };

            var assinarRevalidarLaudoAvaliacaoRevalidacao = function (idLaudo) {
                return $http.put(URL_LAUDO_AVALIACAO + '/assinar/revalidacao/' + idLaudo);
            };

            var recusarAssinarRevalidarLaudoAvaliacao = function (idLaudo) {
                return $http.delete(URL_LAUDO_AVALIACAO + '/recusar/revalidacao/' + idLaudo);
            };

            var getOrganizacao = function () {
                return $http.get(URL_LAUDO_AVALIACAO + '/organizacao');
            };

            var verificarLaudoRevalidacao = function (ripImovel, tipoAvaliacao) {
                return $http.get(URL_LAUDO_AVALIACAO + '/verificar/laudoRevalidacao/', {
                    params: {
                        ripImovel: ripImovel,
                        tipoAvaliacao: tipoAvaliacao
                    }
                });
            };

            var buscarHistoricoAcoes = function (idLaudo) {
                return $http.get(URL_LAUDO_AVALIACAO + '/buscar/historicoAcoes/' + idLaudo);
            };

            var buscarTiposMetodologia = function () {
                return $http.get(URL_LAUDO_AVALIACAO + '/tiposMetodologia/find/all');
            };

            var buscarTiposMetodosAvaliacao = function () {
                return $http.get(URL_LAUDO_AVALIACAO + '/tiposMetodoAvaliacao/find/all');
            };

            var downloadNotaTecnicaHomologacao = function (idLaudo) {
                return URL_LAUDO_AVALIACAO + '/relatorio/' + idLaudo;
            };

            var downloadRevalidacaoLaudoAvaliacao = function (idLaudo) {
                return URL_LAUDO_AVALIACAO + '/revalidacao/relatorio/' + idLaudo;
            };

            var removerRascunhoLaudoAvaliacao = function (idLaudo) {
                return $http.delete(URL_LAUDO_AVALIACAO + '/remover-rascunho/' + idLaudo);
            };

            var excluirLaudoAvaliacao = function (idLaudo) {
                return $http.delete(URL_LAUDO_AVALIACAO + '/excluir-laudo/' + idLaudo);
            };

            var excluirPendenciaLaudoAvaliacao = function(idLaudo) {
                return $http.delete(URL_LAUDO_AVALIACAO + '/excluir-pendencia-laudo/' + idLaudo);
            }

            var navigateToImovel = function(rip) {
                window.open('#/incorporacao/detalharImovel/' + rip, '_blank');
            };

            var verificarLaudosRevalidados = function (laudoAvaliacao, verificarPenteChefia) {
                if (typeof verificarPenteChefia === 'undefined') {
                    verificarPenteChefia = false;
                }

                return laudoAvaliacao.isRevalidado && !verificarPenteChefia;
            }

            return {
                buscarTiposAvaliacaoLaudo: buscarTiposAvaliacaoLaudo,
                salvar: salvar,
                buscarLaudosRelatorioAvaliacao: buscarLaudosRelatorioAvaliacao,
                consultarLaudoAvaliacaoId: consultarLaudoAvaliacaoId,
                revalidarLaudoAvaliacao: revalidarLaudoAvaliacao,
                validarDataAssinaturaCadastrada: validarDataAssinaturaCadastrada,
                cancelarLaudoAvaliacao: cancelarLaudoAvaliacao,
                buscarTiposElaboracaoLaudo: buscarTiposElaboracaoLaudo,
                buscarFonteValorTerreno: buscarFonteValorTerreno,
                buscarTipoNotaTecnicaHomologacao: buscarTipoNotaTecnicaHomologacao,
                buscarTipoNotaTecnicaRevalidacaoLaudo: buscarTipoNotaTecnicaRevalidacaoLaudo,
                assinarLaudoAvaliacaoNotaTecnica: assinarLaudoAvaliacaoNotaTecnica,
                recusarLaudoAvaliacaoNotaTecnica: recusarLaudoAvaliacaoNotaTecnica,
                revalidarLaudoAvaliacaoRevalidacao: revalidarLaudoAvaliacaoRevalidacao,
                assinarRevalidarLaudoAvaliacaoRevalidacao: assinarRevalidarLaudoAvaliacaoRevalidacao,
                recusarAssinarRevalidarLaudoAvaliacao: recusarAssinarRevalidarLaudoAvaliacao,
                buscarSituacoesLaudo: buscarSituacoesLaudo,
                getOrganizacao: getOrganizacao,
                buscarMotivosCancelamento: buscarMotivosCancelamento,
                verificarLaudoRevalidacao: verificarLaudoRevalidacao,
                salvarComoRascunhoRevalidacao: salvarComoRascunhoRevalidacao,
                buscarHistoricoAcoes: buscarHistoricoAcoes,
                buscarTiposMetodologia: buscarTiposMetodologia,
                buscarTiposMetodosAvaliacao: buscarTiposMetodosAvaliacao,
                downloadNotaTecnicaHomologacao: downloadNotaTecnicaHomologacao,
                downloadRevalidacaoLaudoAvaliacao: downloadRevalidacaoLaudoAvaliacao,
                verificarLaudoCadastrado: verificarLaudoCadastrado,
                removerRascunhoLaudoAvaliacao: removerRascunhoLaudoAvaliacao,
                excluirLaudoAvaliacao: excluirLaudoAvaliacao,
                excluirPendenciaLaudoAvaliacao: excluirPendenciaLaudoAvaliacao,
                getUltimaRevalidacao: getUltimaRevalidacao,
                navigateToImovel: navigateToImovel,
                buscarLaudoContabil: buscarLaudoContabil,
                validarValorImovel: validarValorImovel,
                verificarLaudosRevalidados: verificarLaudosRevalidados,
                definirValorTerrenoSemEdificacoes: definirValorTerrenoSemEdificacoes,
                verificarCancelarLaudoAvaliacao: verificarCancelarLaudoAvaliacao
            }
        }]);
}());

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('detalharLaudoAvaliacaoController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "$state", "avaliacaoLaudoService", "numeroExtensoAvaliacaoService", "arquivoAvaliacaoService", "usuarioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "consultaAcoesAvaliacaoService", "imovelAvaliacaoService", function ($scope, laudoAvaliacaoService, $stateParams, $state, avaliacaoLaudoService, numeroExtensoAvaliacaoService, arquivoAvaliacaoService, usuarioAvaliacaoService, avaliacaoEscopoCompartilhadoService, consultaAcoesAvaliacaoService, imovelAvaliacaoService) {

                var vm = $scope;

                vm.LAUDO_AVALIACAO = 'LAUDO_AVALIACAO';
                vm.LAUDO_RELATORIO_VALOR_REFERENCIA = 'LAUDO_RELATORIO_VALOR_REFERENCIA';
                vm.LAUDO_AVALIACAO_LOCACAO = 'LAUDO_AVALIACAO_LOCACAO';
                vm.INFORMAR_VALORES_LAUDO = 'INFORMAR_VALORES_LAUDO';
                vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';
                vm.POR_TERCEIROS = 'POR_TERCEIROS';
                vm.PREENCHER_SISTEMA = 'PREENCHER_SISTEMA';
                vm.UPLOAD_ARQUIVO = 'UPLOAD_ARQUIVO';
                vm.DOCUMENTO_SEI = 'DOCUMENTO_SEI';
                vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';
                vm.REFERENCIA_INCRA = 'REFERENCIA_INCRA';
                vm.RURAL = 'Rural';
                vm.BRASIL = 'Brasil';

                vm.tipoValorLaudo = null;
                vm.listaBenfeitoriasImovel = [];
                vm.unidadeAutonomaImovel = null;

                vm.laudoAvaliacao = {};
                vm.imovel = {};
                vm.formularioRevalidarLaudo = {};

                var init = function () {
                    vm.detalharLaudoRelatorio = true;
                    vm.rip = $stateParams.rip;
                    vm.idLaudo = $stateParams.idLaudo;

                    consultarLaudoAvaliacao();

                };

                var buscarDadosImovel = function () {
                    imovelAvaliacaoService.consultarImovelPorRip(vm.rip).then(function (resposta) {
                        vm.imovel = resposta.data;
                    }).catch(function (erro) {
                        console.error("Erro ao carregar imóvel:", erro);
                    });
                };

                var buscarMoedas = function (callback) {
                    avaliacaoLaudoService.getMoedas().then(function (resp) {
                        vm.listaMoedas = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                var consultarLaudoAvaliacao = function () {
                    buscarTiposAvaliacaoLaudo();
                    buscarMotivosCancelamento();
                    buscarTiposElaboracaoLaudo();
                    buscarTiposAvaliacaoLaudo();
                    buscarFonteValorTerreno();
                    buscarTipoNotaTecnicaHomologacao();
                    buscarTiposMetodologia();
                    buscarTiposMetodosAvaliacao();
                    buscarTiposValores();

                    if (vm.rip && vm.idLaudo) {
                        laudoAvaliacaoService.consultarLaudoAvaliacaoId(vm.idLaudo).then(function (resp) {
                            vm.laudoAvaliacao = resp.data;
                            vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp = vm.laudoAvaliacao.tipoNotaTecnicaHomologacao;
                            vm.laudoAvaliacao.moeda = angular.copy(resp.data.moeda);
                            vm.laudoAvaliacao.valorAdotadoTemp = vm.laudoAvaliacao.valorAdotado;
                            vm.laudoAvaliacao.valorImovelTemp = vm.laudoAvaliacao.valorImovel;
                            vm.laudoAvaliacao.outroValorAdotadoTemp = vm.laudoAvaliacao.outroValorAdotado;
                            if (vm.laudoAvaliacao.dataAssinatura != null) {
                                vm.laudoAvaliacao.dataAssinatura = new Date(resp.data.dataAssinatura);
                                vm.laudoAvaliacao.dataAssinaturaTemp = new Date(resp.data.dataAssinatura);
                            } else {
                                vm.laudoAvaliacao.dataAssinatura = undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                            }
                            if (vm.laudoAvaliacao.dataCancelamento != null) {
                                vm.laudoAvaliacao.dataCancelamento = new Date(resp.data.dataCancelamento);
                            } else {
                                vm.laudoAvaliacao.dataCancelamento = undefined;
                            }

                            buscarDadosImovel();

                            buscarMoedas(function () {
                                for (var i = 0; i < vm.listaMoedas.length; i++) {
                                    if (vm.laudoAvaliacao.moeda.id === vm.listaMoedas[i].id) {
                                        vm.laudoAvaliacao.moeda = vm.listaMoedas[i];
                                    }
                                }
                            });

                            buscarListaOrgaos(function () {
                                buscarListaUnidades();
                            });
                        });
                    }
                };

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                var buscarTiposAvaliacaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposAvaliacaoLaudo().then(function (resp) {
                        vm.listaTiposAvaliacao = resp.data;
                    });
                };

                var buscarMotivosCancelamento = function () {
                    laudoAvaliacaoService.buscarMotivosCancelamento().then(function (resp) {
                        vm.listaMotivosCancelamento = resp.data;
                    });
                };

                var buscarTiposElaboracaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposElaboracaoLaudo().then(function (resp) {
                        vm.listaElaboracaoLaudo = resp.data;
                    });
                };

                var buscarTipoNotaTecnicaHomologacao = function () {
                    vm.listaTipoNotaTecnicaHomologacao = {
                        UPLOAD_ARQUIVO: "Upload de Arquivo (PDF)",
                        PREENCHER_SISTEMA: "Preencher no Sistema",
                        DOCUMENTO_SEI: "Utilizar Documento SEI"

                    };
                };

                var buscarFonteValorTerreno = function () {
                    laudoAvaliacaoService.buscarFonteValorTerreno().then(function (resp) {
                        vm.listaFonteValorTerreno = resp.data;
                    });
                };

                var buscarTiposMetodologia = function () {
                    vm.listaTiposMetodologia = {
                        INFERENCIA_ESTATISTICA: "Inferência estatística",
                        ESTIMATIVA_VALORES_TERRENO_BENFEITORIA: "Estimativa de valores de terreno e benfeitoria",
                        GLEBA_URBANIZAVEL: "Gleba Urbanizável",
                        OUTRO: "Outro"
                    };
                };

                var buscarTiposMetodosAvaliacao = function () {
                    vm.listaTiposMetodosAvaliacao = {
                        COMPARATIVO_MERCADO: "Comparativo direto de dados de mercado",
                        EVOLUTIVO: "Evolutivo",
                        INVOLUTIVO: "Involutivo",
                        CAPITALIZACAO_RENDA: "Capitalização da renda"
                    };
                };

                vm.downloadNotaTecnicaHomologacao = function () {
                    return laudoAvaliacaoService.downloadNotaTecnicaHomologacao(vm.idLaudo);
                };

                vm.downloadRevalidacaoLaudoAvaliacao = function () {
                    return laudoAvaliacaoService.downloadRevalidacaoLaudoAvaliacao(vm.idLaudo);
                };

                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService.converterNumeroPorExtenso(numero, vm.laudoAvaliacao.moeda.nome.toLowerCase());
                };

                vm.exibirInserirValoresTerrenoBenfeitoria = function () {
                    return (vm.laudoAvaliacao.benfeitoriasImovel && vm.laudoAvaliacao.benfeitoriasImovel.length > 0) || (vm.laudoAvaliacao.unidadeAutonomaIncorporacao && vm.laudoAvaliacao.unidadeAutonomaIncorporacao.length > 0);
                };

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                vm.ripImovelLink = function (rip) {
                    laudoAvaliacaoService.navigateToImovel(rip);
                };

                vm.visualizarDocumentoRevalidacaoLaudo = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('idLaudo', vm.laudoAvaliacao.id);
                    avaliacaoEscopoCompartilhadoService.setObjetos('revalidacaoLaudoAvaliacao', vm.laudoAvaliacao.revalidacaoLaudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriasImovel', vm.listaBenfeitoriasImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaImovel', vm.laudoAvaliacao.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoValorLaudo', vm.laudoAvaliacao.tipoValorLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('orgao', vm.laudoAvaliacao.orgao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidade', vm.laudoAvaliacao.unidade);
                    avaliacaoEscopoCompartilhadoService.setObjetos('subunidade', vm.laudoAvaliacao.subunidade);
                    avaliacaoEscopoCompartilhadoService.setObjetos('detalharLaudoRelatorio', true);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioRevalidarLaudo', vm.formularioRevalidarLaudo);

                    $state.go('avaliacao.VISUALIZAR_DOCUMENTO_REVALIDAR_LAUDO_AVALIACAO');
                };

                vm.visualizarDocumentoNotaTecnica = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriasImovel', vm.listaBenfeitoriasImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaImovel', vm.laudoAvaliacao.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoValorLaudo', vm.laudoAvaliacao.tipoValorLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('notaTecnicaHomologacao', vm.laudoAvaliacao.notaTecnicaHomologacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('detalharLaudoRelatorio', true);

                    consultaAcoesAvaliacaoService.consultarAcoesAvaliacaoLaudo(vm.idLaudo).then(function (resp) {
                        var acaoAvaliacaoList = resp.data;

                        if (acaoAvaliacaoList != null & acaoAvaliacaoList.length > 0) {
                            avaliacaoEscopoCompartilhadoService.setObjetos('acaoAvaliacao', acaoAvaliacaoList.filter(function (acao) {
                                return (acao.tipoAcaoAvaliacao === 'AVALIACAO_LAUDO' || acao.tipoAcaoAvaliacao === 'AVALIACAO_LAUDO_LOCACAO') && acao.tipoCargo === 'CHEFIA' && acao.decisao != null;
                            }));
                        }
                        $state.go('avaliacao.VISUALIZAR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO');
                    }, function () {
                        $state.go('avaliacao.VISUALIZAR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO');
                    });

                };

                vm.labelTipoLaudo = function () {
                    if (vm.laudoAvaliacao.tipoLaudoElaborado === 'LAUDO_AVALIACAO' || vm.laudoAvaliacao.tipoLaudoElaborado === 'LAUDO_AVALIACAO_LOCACAO') {
                        return 'Laudo elaborado';
                    } else {
                        return 'Relatório elaborado';
                    }

                };

                var buscarListaOrgaos = function (callback) {
                    usuarioAvaliacaoService.getOrgaos().then(function (resp) {
                        vm.listaOrgaos = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                var buscarListaUnidades = function () {
                    usuarioAvaliacaoService.getUnidadesUsuario().then(function (resp) {
                        vm.listaUnidades = resp.data;
                    });
                };

                var buscarTiposValores = function () {
                    avaliacaoLaudoService.ordenacaoTipoValor().then(function (resp) {
                        vm.listaTiposValores = resp.data;
                    });
                };

                vm.verificarRevalidacoes = function (laudoAvaliacao) {
                    return laudoAvaliacaoService.verificarLaudosRevalidados(laudoAvaliacao);
                }

                init();
            }]);

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cancelarLaudoAvaliacaoController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "comumAvaliacaoService", "$state", "formularioAvaliacaoService", "$filter", "$mdDialog", "pvgSpuService", "pvgAvaliacaoService", "ComumFactory", function ($scope, laudoAvaliacaoService, $stateParams, comumAvaliacaoService, $state, formularioAvaliacaoService,
                      $filter, $mdDialog, pvgSpuService, pvgAvaliacaoService, ComumFactory) {

                var vm = $scope;
                vm.rip = $stateParams.rip;
                vm.id = $stateParams.idLaudo;
                vm.selecioneMotivo = '';
                vm.laudoAvaliacao = {};
                vm.data = new Date();
                vm.justificativa = '';

                var init = function () {

                    laudoAvaliacaoService.consultarLaudoAvaliacaoId(vm.id).then(function (resp) {
                        vm.laudoAvaliacao.tipoAvaliacao = resp.data.tipoAvaliacao;
                        if (vm.laudoAvaliacao.tipoAvaliacao === 'LAUDO_RELATORIO_VALOR_REFERENCIA') {
                            vm.titulo = $filter('translate')('title-cancelar-relatorio-valor-referencia');
                        } else if (vm.laudoAvaliacao.tipoAvaliacao === 'LAUDO_AVALIACAO') {
                            vm.titulo = $filter('translate')('title-cancelar-laudo-avaliacao');
                        } else if (vm.laudoAvaliacao.tipoAvaliacao === 'LAUDO_AVALIACAO_LOCACAO') {
                            vm.titulo = $filter('translate')('title-cancelar-laudo-avaliacao-locacao');
                        }
                    });

                    vm.dataMostrar = $filter('date')(new Date(), 'dd/MM/yyyy');

                    comumAvaliacaoService.usuarioLogado().then(function (resp) {
                        vm.nomeResponsavel = resp.data;
                    });

                    comumAvaliacaoService.consultarMotivosCancelar()
                        .then(function (resp) {
                            vm.motivosCancelar = resp.data;
                        });
                };

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;
                var confirm = {}

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                vm.cancelar = function (formularioCancelarLaudoRelatorio) {
                    vm.validarFormulario(formularioCancelarLaudoRelatorio);
                    if (!formularioCancelarLaudoRelatorio.$valid) {
                        return;
                    }

                    if (vm.laudoAvaliacao.tipoAvaliacao === 'LAUDO_RELATORIO_VALOR_REFERENCIA') {
                        confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent("Após cancelar o relatório de valor de referência, este não poderá mais ser ativado. Deseja continuar? ")
                            .ok("Confirmar")
                            .cancel("Cancelar");
                    } else if (vm.laudoAvaliacao.tipoAvaliacao === 'LAUDO_AVALIACAO') {
                        confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent("Após cancelar o laudo de avaliação, este não poderá mais ser ativado. Deseja continuar?")
                            .ok("Confirmar")
                            .cancel("Cancelar");
                    } else if (vm.laudoAvaliacao.tipoAvaliacao === 'LAUDO_AVALIACAO_LOCACAO') {
                        confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent("Após cancelar o laudo de avaliação para locação/arrendamento, este não poderá mais ser ativado. Deseja continuar?")
                            .ok("Confirmar")
                            .cancel("Cancelar");
                    }

                    $mdDialog.show(confirm).then(function () {
                        vm.laudoAvaliacao.dataCancelamento = vm.data;
                        vm.laudoAvaliacao.justificativaCancelamento = vm.justificativa;
                        vm.laudoAvaliacao.motivoCancelamento = vm.selecioneMotivo;
                        vm.laudoAvaliacao.id = vm.id;
                        vm.laudoAvaliacao.ripImovel = vm.rip;
                        laudoAvaliacaoService.cancelarLaudoAvaliacao(vm.laudoAvaliacao).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                            $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                ripDetalhar: vm.rip
                            });
                        });
                    });

                };

                init();
            }])
})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cadastrarRelatorioValorReferenciaController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "imovelAvaliacaoService", "$state", "formularioAvaliacaoService", "$filter", "$mdDialog", "ComumFactory", "avaliacaoEscopoCompartilhadoService", "numeroExtensoAvaliacaoService", "usuarioAvaliacaoService", "documentoSeiService", function ($scope, laudoAvaliacaoService, $stateParams, imovelAvaliacaoService, $state, formularioAvaliacaoService,
                      $filter, $mdDialog, ComumFactory, avaliacaoEscopoCompartilhadoService, numeroExtensoAvaliacaoService, usuarioAvaliacaoService, documentoSeiService) {

                var vm = $scope;
                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.nomeCampo = $filter('translate')('laudo-relatorio');
                vm.labelVermelho = {color: 'red'};
                vm.listaTiposAvaliacao = [];
                vm.listaBenfeitoriasImovel = [];
                vm.unidadeAutonomaImovel = null;

                vm.LAUDO_AVALIACAO = 'LAUDO_AVALIACAO';
                vm.PELO_ORGAO_ENTIDADE = 'PELO_ORGAO_ENTIDADE';
                vm.LAUDO_RELATORIO_VALOR_REFERENCIA = 'LAUDO_RELATORIO_VALOR_REFERENCIA';
                vm.INFORMAR_VALORES_LAUDO = 'INFORMAR_VALORES_LAUDO';
                vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';
                vm.POR_TERCEIROS = 'POR_TERCEIROS';
                vm.PREENCHER_SISTEMA = 'PREENCHER_SISTEMA';
                vm.UPLOAD_ARQUIVO = 'UPLOAD_ARQUIVO';
                vm.DOCUMENTO_SEI = 'DOCUMENTO_SEI';
                vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';
                vm.REFERENCIA_INCRA = 'REFERENCIA_INCRA';
                vm.LAUDO_AVALIACAO_AQUISICAO = 'LAUDO_AVALIACAO_AQUISICAO';
                vm.RURAL = 'Rural';
                vm.BRASIL = 'Brasil';

                var HECTARE = 'HECTARE';

                vm.rip = $stateParams.rip;
                vm.imovel = {};
                vm.detalharLaudoRelatorio = false;

                vm.laudoAvaliacao = {};
                vm.laudoAvaliacao.responsaveis = [];
                vm.responsavelTecnico = {};
                vm.laudoAvaliacao.tipoValorLaudo = null;

                vm.laudoAvaliacao.valorTotalTerreno = 0;

                vm.valorInvalido = false;

                vm.listaMoedas = [
                    {
                        id: 1,
                        codigo: '001',
                        nome: 'Real',
                        simbolo: 'R$',
                        codigoPais: '55',
                        pais: 'Brasil',
                        dataExclusao: new Date()
                    }, {
                        id: 2,
                        codigo: '002',
                        nome: 'Dolar',
                        simbolo: '$',
                        codigoPais: '00',
                        pais: 'Estados Unidos',
                        dataExclusao: new Date()
                    }
                ];

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO === 'false' && vm.usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO_NAO_HABILITADO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                var init = function () {
                    vm.laudoAvaliacao.tipoAvaliacao = vm.LAUDO_RELATORIO_VALOR_REFERENCIA;

                    buscarUsuarioLogado(function () {
                        //buscarListaOrgaos();
                        vm.buscarListaUnidades();
                        buscarTiposAvaliacaoLaudo();
                        buscarTiposMetodologia();
                        buscarTiposMetodosAvaliacao();
                        buscarFonteValorTerreno();
                        vm.buscarTiposElaboracaoLaudo();

                        if ($state.current.name.indexOf("EDITAR_RELATORIO_VALOR_REFERENCIA") !== -1) {
                            consultarLaudoAvaliacao();
                            buscarDadosImovel();
                            vm.laudoAvaliacao.tipoValorLaudo = vm.INFORMAR_VALORES_LAUDO;
                        } else {
                            if (avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao')) {

                                vm.rip = avaliacaoEscopoCompartilhadoService.getObjeto('rip');
                                vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                                vm.laudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao');
                                vm.orgao = avaliacaoEscopoCompartilhadoService.getObjeto('orgao');
                                vm.unidade = avaliacaoEscopoCompartilhadoService.getObjeto('unidade');
                                vm.subunidade = avaliacaoEscopoCompartilhadoService.getObjeto('subunidade');
                                if (vm.laudoAvaliacao.laudoRelatorioAvaliacao) {
                                    if (vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao) {
                                        vm.laudoAvaliacao.valorImovel = vm.validarValorLaudoRelatorio(vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor, vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao);
                                        vm.laudoAvaliacao.tipoValorLaudo = vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.tipoValorLaudo;
                                    }
                                    vm.unidadeMedida = vm.laudoAvaliacao.laudoRelatorioAvaliacao.terrenoImovel.terreno.unidadeMedida === HECTARE ? 'ha' : 'm²';
                                    vm.laudoAvaliacao.valorTerreno = vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.valorTerreno;
                                    vm.laudoAvaliacao.unidadeAutonomaIncorporacao = vm.laudoAvaliacao.laudoRelatorioAvaliacao.unidadeAutonoma.unidadeAutonomaIncorporacao;
                                    var benfeitoriasImovel = [];
                                    angular.forEach(vm.laudoAvaliacao.laudoRelatorioAvaliacao.benfeitorias.benfeitoriasAvaliacao, function (benfeitoriasAvaliacao) {
                                        benfeitoriasImovel.push(benfeitoriasAvaliacao.benfeitoriaImovel);
                                    });
                                    vm.laudoAvaliacao.benfeitoriasImovel = benfeitoriasImovel;
                                    vm.laudoAvaliacao.dataAssinatura = vm.laudoAvaliacao.dataAssinatura ? new Date(vm.laudoAvaliacao.dataAssinatura) : new Date(vm.laudoAvaliacao.laudoRelatorioAvaliacao.dataAssinatura);
                                    vm.laudoAvaliacao.dataAssinaturaTemp = vm.laudoAvaliacao.dataAssinatura ? new Date(vm.laudoAvaliacao.dataAssinatura) : new Date(vm.laudoAvaliacao.laudoRelatorioAvaliacao.dataAssinatura);
                                    vm.laudoAvaliacao.responsaveis = vm.laudoAvaliacao.laudoRelatorioAvaliacao.identificacao ? vm.laudoAvaliacao.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos : vm.laudoAvaliacao.responsaveis;
                                }

                                vm.dataTemp = new Date(vm.laudoAvaliacao.dataAssinatura).getTime();
                                vm.laudoAvaliacao.dataAssinatura = undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                                vm.laudoAvaliacao.dataAssinatura = vm.dataTemp ? new Date(vm.dataTemp) : undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = vm.dataTemp ? new Date(vm.dataTemp) : undefined;

                                avaliacaoEscopoCompartilhadoService.limparEscopo();

                            } else {
                                buscarDadosImovel();
                                buscarListaBenfeitoriasSu();
                                buscarDadosUnidadeAutonomaIncorporacao();
                            }
                        }
                    });

                };

                var consultarLaudoAvaliacao = function () {

                    vm.rip = $stateParams.rip;
                    vm.idLaudo = $stateParams.idLaudo;

                    if (vm.rip && vm.idLaudo) {
                        laudoAvaliacaoService.consultarLaudoAvaliacaoId(vm.idLaudo).then(function (resp) {
                            vm.laudoAvaliacao = resp.data;
                            vm.laudoAvaliacao.valorImovelTemp = vm.laudoAvaliacao.valorImovel;
                            vm.laudoAvaliacao.moeda = angular.copy(resp.data.moeda);
                            if (vm.laudoAvaliacao.dataAssinatura != null) {
                                vm.laudoAvaliacao.dataAssinatura = new Date(resp.data.dataAssinatura);
                                vm.laudoAvaliacao.dataAssinaturaTemp = new Date(resp.data.dataAssinatura);
                            } else {
                                vm.laudoAvaliacao.dataAssinatura = undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                            }
                            if (!vm.laudoAvaliacao.benfeitoriasImovel) {
                                buscarListaBenfeitoriasSu();
                            }
                            vm.buscarListaUnidades();
                        });
                    }
                };

                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService.converterNumeroPorExtenso(numero, vm.laudoAvaliacao.moeda.nome.toLowerCase());
                };

                vm.validarValorLaudoRelatorio = function (tipoValor, resultadoAvaliacao) {
                    var VALOR_MEDIO = 'VALOR_MEDIO';
                    var VALOR_MINIMO = 'VALOR_MINIMO';
                    var VALOR_MAXIMO = 'VALOR_MAXIMO';
                    var OUTRO_VALOR = 'OUTRO_VALOR';
                    if (tipoValor == VALOR_MEDIO) {
                        return resultadoAvaliacao.valorMedio;
                    } else if (tipoValor == VALOR_MINIMO) {
                        return resultadoAvaliacao.valorMinimo;
                    } else if (tipoValor == VALOR_MAXIMO) {
                        return resultadoAvaliacao.valorMaximo;
                    } else if (tipoValor == OUTRO_VALOR) {
                        return resultadoAvaliacao.outroValorAdotado;
                    }
                };

                vm.inserirResponsavelTecnico = function () {
                    vm.laudoAvaliacao.responsaveis.push(angular.copy(vm.responsavelTecnico));
                };

                var buscarTiposAvaliacaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposAvaliacaoLaudo().then(function (resp) {
                        vm.listaTiposAvaliacao = resp.data;
                    });
                };

                vm.buscarTiposElaboracaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposElaboracaoLaudo().then(function (resp) {
                        vm.listaElaboracaoLaudo = resp.data;
                    });
                };

                vm.changeTipoAvaliacao = function () {
                    vm.laudoAvaliacao.tipoLaudoElaborado = null;
                    vm.laudoAvaliacao.tipoMetodologia = null;
                    vm.laudoAvaliacao.tipoMetodoAvaliacao = null;
                    vm.laudoAvaliacao.metodologiaOutro = null;
                }

                var buscarTiposMetodologia = function () {
                    vm.listaTiposMetodologia = {
                        INFERENCIA_ESTATISTICA: "Inferência estatística",
                        ESTIMATIVA_VALORES_TERRENO_BENFEITORIA: "Estimativa de valores de terreno e benfeitoria",
                        GLEBA_URBANIZAVEL: "Gleba Urbanizável",
                        OUTRO: "Outro"
                    }
                };

                var buscarTiposMetodosAvaliacao = function () {
                    vm.listaTiposMetodosAvaliacao = {
                        COMPARATIVO_MERCADO: "Comparativo direto de dados de mercado",
                        EVOLUTIVO: "Evolutivo",
                        INVOLUTIVO: "Involutivo",
                        CAPITALIZACAO_RENDA: "Capitalização da renda"
                    }
                };

                var buscarFonteValorTerreno = function () {
                    laudoAvaliacaoService.buscarFonteValorTerreno().then(function (resp) {
                        vm.listaFonteValorTerreno = resp.data;
                    });
                };

                var buscarDadosImovel = function () {
                    imovelAvaliacaoService.consultarImovelPorRip(vm.rip).then(function (resposta) {
                        vm.imovel = resposta.data;
                        vm.unidadeMedida = (vm.imovel.terreno.geometriaTerrenoCartorial ? vm.imovel.terreno.unidadeMedidaCartorial : vm.imovel.terreno.unidadeMedidaCadastral);
                        vm.unidadeMedida = vm.unidadeMedida == HECTARE ? 'ha' : 'm²';
                        validarCampoMoeda();
                        buscarIncorporacaoImovel();
                    });
                };

                var buscarIncorporacaoImovel = function () {
                    imovelAvaliacaoService.buscarDadosGeometriaIncorporacao(vm.imovel.rip).then(function (resp) {
                        vm.imovel.incorporacao = resp.data;
                    });
                };

                var buscarListaBenfeitoriasSu = function () {
                    if (vm.rip) {
                        imovelAvaliacaoService.buscarBenfeitoriasSuPorRip(vm.rip).then(function (resp) {
                            vm.laudoAvaliacao.benfeitoriasImovel = resp.data;
                        });
                    }
                };

                vm.exibirInserirValoresTerrenoBenfeitoria = function () {
                    return (vm.laudoAvaliacao.benfeitoriasImovel && vm.laudoAvaliacao.benfeitoriasImovel.length > 0) || (vm.laudoAvaliacao.unidadeAutonomaIncorporacao && vm.laudoAvaliacao.unidadeAutonomaIncorporacao.length > 0);
                };

                var buscarDadosUnidadeAutonomaIncorporacao = function () {
                    if (vm.rip) {
                        imovelAvaliacaoService.buscarUnidadeAutonomaSuPorRip(vm.rip).then(function (resp) {
                            if (resp.data === "") {
                                vm.laudoAvaliacao.unidadeAutonomaIncorporacao = [];
                            } else {
                                vm.laudoAvaliacao.unidadeAutonomaIncorporacao = [];
                                vm.laudoAvaliacao.unidadeAutonomaIncorporacao.push(resp.data);
                            }
                        });
                    }
                };

                vm.validarCPFInformado = function (cpf) {
                    if (!cpf) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-cpf-invalido'));
                    }
                };

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                vm.validarDataAssinaturaPosteriorDataAtual = function () {
                    if (vm.laudoAvaliacao.dataAssinatura > new Date()) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-assinatura-posterior-data-atual'));
                        vm.laudoAvaliacao.dataAssinatura = undefined;
                        vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                        throw $filter('translate')('mensagem-data-assinatura-posterior-data-atual');
                    }
                };

                vm.validarDataAssinaturaOutroLaudo = function () {
                    if (vm.laudoAvaliacao.dataAssinatura) {
                        return laudoAvaliacaoService.validarDataAssinaturaCadastrada(
                            vm.laudoAvaliacao.tipoAvaliacao,
                            vm.laudoAvaliacao.dataAssinatura,
                            vm.rip,
                            vm.laudoAvaliacao.id || null
                        ).then(function () {
                            return Promise.resolve();
                        }).catch(function () {
                            if (
                                vm.laudoAvaliacao.dataAssinatura == undefined || vm.laudoAvaliacao.dataAssinatura == null ||
                                vm.laudoAvaliacao.dataAssinaturaTemp == undefined || vm.laudoAvaliacao.dataAssinaturaTemp == null
                            ) {
                                return;
                            }
                            vm.laudoAvaliacao.dataAssinatura = null;
                            vm.laudoAvaliacao.dataAssinaturaTemp = null;
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-assinatura-outro-laudo'));
                            return Promise.reject();
                        });
                    }

                    return Promise.resolve();
                };

                var validarCampoMoeda = function () {
                    if (vm.imovel.enderecamento) {
                        if (vm.imovel.enderecamento.pais == vm.BRASIL) {
                            vm.laudoAvaliacao.moeda = vm.listaMoedas[0];
                        }
                    }
                };

                var preencherDadosLaudoAvaliacao = function () {
                    vm.laudoAvaliacao.ripImovel = vm.rip;
                };

                var validarCreaHomologadoresInformados = function () {
                    if (vm.laudoAvaliacao.tipoLaudoElaborado != 'POR_TERCEIROS') {
                        return false;
                    }
                    if (verificaResponsaveisHomologadoresIguais()) {
                        return true;
                    }
                    var creaPendente = false;
                    if (vm.laudoAvaliacao.homologadores.length === 0) {
                        creaPendente = true;
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-necessario-informar-homologador'));
                    }

                    return creaPendente;
                };

                var verificaResponsaveisHomologadoresIguais = function () {
                    if (vm.laudoAvaliacao.responsaveis.length > 0) {
                        for (var i = 0; i < vm.laudoAvaliacao.homologadores.length; i++) {
                            for (var j = 0; j < vm.laudoAvaliacao.responsaveis.length; j++) {
                                if (vm.laudoAvaliacao.homologadores[i].cpf === vm.laudoAvaliacao.responsaveis[j].cpf) {
                                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-mesmo-responsavel-homologador'));
                                    return true;
                                }
                            }
                        }
                    }
                    return false;
                };

                vm.verificarHomologador = function () {
                    for (var i in vm.laudoAvaliacao.homologadores) {
                        if (vm.laudoAvaliacao.homologadores[i]) {
                            if (vm.laudoAvaliacao.homologadores[i].cpf === vm.usuarioLogado.cpf) {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                vm.ripImovelLink = function (rip) {
                    laudoAvaliacaoService.navigateToImovel(rip);
                };

                vm.valoresBenfeitoriasPreenchidos = function () {
                    if (vm.laudoAvaliacao.benfeitoriasImovel){
                        for (var i = 0; i < vm.laudoAvaliacao.benfeitoriasImovel.length; i++) {
                            if (vm.laudoAvaliacao.benfeitoriasImovel[i].valor == null) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-benfeitoria-nulo'));
                                return false;
                            }
                        };
                    }
                    return true;
                }

                vm.salvarLaudoAvaliacao = function (formularioDadosLaudo) {

                    if (!vm.valoresBenfeitoriasPreenchidos()){
                        return;
                    }

                    if (formularioDadosLaudo) {
                        vm.validarFormulario(formularioDadosLaudo);
                            if (!formularioDadosLaudo.$valid) {
                            return;
                        }
                    }

                    if (angular.isDefined(vm.laudoAvaliacao.valorImovel) && angular.isDefined(vm.laudoAvaliacao.valorTerreno) && angular.isDefined(vm.laudoAvaliacao.valorTotalTerreno)) {
                        if (vm.laudoAvaliacao.tipoValorLaudo === vm.INFORMAR_VALORES_LAUDO) {
                            if (vm.laudoAvaliacao.valorTerreno > vm.laudoAvaliacao.valorImovel) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-are-construida-bao-corresponde-valor-imovel'));
                                return;
                            }
                        }
                    }

                    if (vm.laudoAvaliacao.benfeitoriasImovel && vm.laudoAvaliacao.benfeitoriasImovel.length > 0) {
                        // OCULTAR VALIDAÇÃO JÁ QUE O CUB NÃO É OBRIGATÓRIO - IB - 4734672
                        /*
                        for (var i = 0; i < vm.laudoAvaliacao.benfeitoriasImovel.length; i++) {
                            console.log(vm.laudoAvaliacao.benfeitoriasImovel[i])
                            if ( vm.laudoAvaliacao.benfeitoriasImovel[i].padraoAcabamento === null || vm.laudoAvaliacao.benfeitoriasImovel[i].tipoProjetoCub === null ) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-cub'));
                                return;
                            }
                        }*/
                        for (var i = 0; i < vm.laudoAvaliacao.benfeitoriasImovel.length; i++) {
                            if (vm.laudoAvaliacao.benfeitoriasImovel[i].edificacao) {
                                vm.laudoAvaliacao.benfeitoriasImovel[i].edificacao.documentacao = null;
                            }
                        }
                    }

                    if (angular.isDefined(vm.laudoAvaliacao.valorImovel) && angular.isDefined(vm.laudoAvaliacao.valorTerreno) && angular.isDefined(vm.laudoAvaliacao.valorTotalTerreno)) {
                        if (vm.laudoAvaliacao.tipoValorLaudo === vm.ESTIMAR_VALORES) {
                            if (vm.laudoAvaliacao.valorTotalTerreno > vm.laudoAvaliacao.valorImovel || vm.laudoAvaliacao.valorTotalTerreno < 0) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                                return;
                            }
                        }
                    }

                    vm.laudoAvaliacao.situacaoLaudo = null;
                    preencherDadosLaudoAvaliacao();
                    vm.validarDataAssinaturaPosteriorDataAtual();
                    validarCampoMoeda();
                    laudoAvaliacaoService.definirValorTerrenoSemEdificacoes(vm.laudoAvaliacao);

                    if (vm.laudoAvaliacao.responsaveis.length == 0) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-adicionar-responsavel-tecnico'));
                        return;
                    }
                    if (vm.laudoAvaliacao.arquivosLaudoReferencia.length == 0) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-adicionar-relatorio-valor-referencia'));
                        return;
                    }

                    vm.laudoAvaliacao.ufImovel = angular.copy(vm.imovel.enderecamento.uf);
                    laudoAvaliacaoService.verificarLaudoCadastrado(vm.laudoAvaliacao.tipoAvaliacao, vm.rip, vm.laudoAvaliacao.dataAssinatura, vm.laudoAvaliacao.id).then(function (resp) {
                        vm.isLaudoCadastrado = resp.data;
                        if (vm.isLaudoCadastrado) {
                            var confirm = $mdDialog.confirm()
                                .title("Atenção!")
                                .textContent("O Relatório informado está sobrepondo um relatório ja cadastrado que possui destinação vinculada. Deseja continuar?")
                                .ok("Confirmar")
                                .cancel("Cancelar");

                            $mdDialog.show(confirm).then(function () {
                                laudoAvaliacaoService.salvar(vm.laudoAvaliacao).then(function (resp) {
                                    vm.laudoAvaliacao = resp.data;
                                    if (vm.laudoAvaliacao.tipoNotaTecnicaHomologacao === vm.PREENCHER_SISTEMA && vm.laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento && vm.verificarHomologador()) {
                                        laudoAvaliacaoService.assinarLaudoAvaliacaoNotaTecnica(vm.laudoAvaliacao.id).then(function () {
                                        });
                                    }
                                    ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                        ripDetalhar: vm.laudoAvaliacao.ripImovel
                                    });
                                }, function (erro) {
                                ComumFactory.mostrarMensagemError(erro.data.message);
                                });
                            }, function () {
                                vm.laudoAvaliacao.dataAssinaturaTemp = vm.laudoAvaliacao.dataAssinatura;
                                vm.laudoAvaliacao.valorImovelTemp = vm.laudoAvaliacao.valorImovel;
                            });

                        } else {
                            laudoAvaliacaoService.salvar(vm.laudoAvaliacao).then(function (resp) {
                                vm.laudoAvaliacao = resp.data;
                                if (vm.laudoAvaliacao.tipoNotaTecnicaHomologacao === vm.PREENCHER_SISTEMA && vm.laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento && vm.verificarHomologador()) {
                                    laudoAvaliacaoService.assinarLaudoAvaliacaoNotaTecnica(vm.laudoAvaliacao.id).then(function () {
                                    });
                                }
                                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                    ripDetalhar: vm.laudoAvaliacao.ripImovel
                                });
                            }, function (erro) {
                                ComumFactory.mostrarMensagemError(erro.data.message);
                            });
                        }
                    });

                };

                vm.salvarComoRascunhoLaudoAvaliacao = function () {

                    if (vm.laudoAvaliacao.benfeitoriasImovel && vm.laudoAvaliacao.benfeitoriasImovel.length > 0) {
                        for (var i = 0; i < vm.laudoAvaliacao.benfeitoriasImovel.length; i++) {
                            if (vm.laudoAvaliacao.benfeitoriasImovel[i].edificacao) {
                                vm.laudoAvaliacao.benfeitoriasImovel[i].edificacao.documentacao = null;
                            }
                        }
                    }

                    if (angular.isDefined(vm.laudoAvaliacao.valorImovel) && angular.isDefined(vm.laudoAvaliacao.valorTerreno) && angular.isDefined(vm.laudoAvaliacao.valorTotalTerreno)) {
                        if (vm.laudoAvaliacao.tipoValorLaudo === vm.ESTIMAR_VALORES) {
                            if (vm.laudoAvaliacao.valorTotalTerreno > vm.laudoAvaliacao.valorImovel || vm.laudoAvaliacao.valorTotalTerreno < 0) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                                return;
                            }
                        }
                    }

                    preencherDadosLaudoAvaliacao();
                    vm.validarDataAssinaturaPosteriorDataAtual();
                    laudoAvaliacaoService.definirValorTerrenoSemEdificacoes(vm.laudoAvaliacao);

                    vm.laudoAvaliacao.situacaoLaudo = "EM_PREENCHIMENTO";
                    vm.laudoAvaliacao.ufImovel = angular.copy(vm.imovel.enderecamento.uf);
                    laudoAvaliacaoService.salvar(vm.laudoAvaliacao).then(function (resp) {
                        var confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent($filter('translate')('mensagem-rascunho-salvo-com-sucesso-continuar-preenchimento'))
                            .ok("Sim")
                            .cancel("Não");

                        $mdDialog.show(confirm).then(function () {
                            vm.laudoAvaliacao = resp.data;
                            vm.laudoAvaliacao.valorImovelTemp = vm.laudoAvaliacao.valorImovel;
                            vm.laudoAvaliacao.moeda = angular.copy(resp.data.moeda);
                            if (vm.laudoAvaliacao.dataAssinatura != null) {
                                vm.laudoAvaliacao.dataAssinatura = new Date(resp.data.dataAssinatura);
                                vm.laudoAvaliacao.dataAssinaturaTemp = new Date(resp.data.dataAssinatura);
                            } else {
                                vm.laudoAvaliacao.dataAssinatura = undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                            }
                        }, function () {
                            $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                ripDetalhar: vm.rip
                            });
                        });
                    });
                };

                vm.redirecionarInformarValorCub = function (benfeitoria, tipoTerreno) {
                    preencherRipLaudoEscopoCompartilhado();
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', tipoTerreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', benfeitoria);
                    $state.go('avaliacao.INFORMAR_VALOR_CUB');
                };

                var preencherRipLaudoEscopoCompartilhado = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriasImovel', vm.listaBenfeitoriasImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaImovel', vm.laudoAvaliacao.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoValorLaudo', vm.laudoAvaliacao.tipoValorLaudo);
                };

                vm.changeValoresDataValor = function () {
                    if (vm.laudoAvaliacao.tipoValorLaudo && (vm.laudoAvaliacao.valorTerreno || vm.isAlgumaBenfeitoriaPreenchida()) ) {
                        var confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent("Após a alteração desse campo, os valores de Terreno e Benfeitoria deverão ser preenchidos novamente. Deseja continuar?")
                            .ok("Confirmar")
                            .cancel("Cancelar");

                        $mdDialog.show(confirm).then(function () {
                            vm.laudoAvaliacao.dataAssinatura = vm.laudoAvaliacao.dataAssinaturaTemp;
                            vm.laudoAvaliacao.valorImovel = vm.laudoAvaliacao.valorImovelTemp;
                            vm.validarDataAssinaturaOutroLaudo();
                            vm.validarDataAssinaturaPosteriorDataAtual();
                            vm.limparDadosUnidadeAutonomaEBenfeitorias();
                        }, function () {
                            vm.laudoAvaliacao.dataAssinaturaTemp = vm.laudoAvaliacao.dataAssinatura;
                            vm.laudoAvaliacao.valorImovelTemp = vm.laudoAvaliacao.valorImovel;
                        });
                    } else {
                        vm.laudoAvaliacao.dataAssinatura = vm.laudoAvaliacao.dataAssinaturaTemp;
                        vm.laudoAvaliacao.valorImovel = vm.laudoAvaliacao.valorImovelTemp;
                        vm.validarDataAssinaturaOutroLaudo();
                        vm.validarDataAssinaturaPosteriorDataAtual();
                    }
                };

                vm.limparDadosUnidadeAutonomaEBenfeitorias = function () {
                    vm.laudoAvaliacao.tipoValorLaudo = null;
                    if (vm.laudoAvaliacao.benfeitoriasImovel && vm.laudoAvaliacao.benfeitoriasImovel.length > 0) {
                        angular.forEach(vm.laudoAvaliacao.benfeitoriasImovel, function (benfeitoria) {
                            benfeitoria.valor = null;
                            benfeitoria.padraoAcabamento = null;
                            benfeitoria.tipoProjetoCub = null;
                            benfeitoria.idadeAparente = null;
                            benfeitoria.vidaUtil = null;
                            benfeitoria.estadoConservacao = null;
                            benfeitoria.valorCub = null;
                            benfeitoria.valorCubDepreciado = null;
                            benfeitoria.estimativas = null;
                            benfeitoria.isFatoresAcrescimo = null;
                        });
                    } else if (vm.laudoAvaliacao.unidadeAutonomaIncorporacao && vm.laudoAvaliacao.unidadeAutonomaIncorporacao.length > 0) {
                        vm.laudoAvaliacao.unidadeAutonomaIncorporacao[0].valor = null;
                        vm.laudoAvaliacao.unidadeAutonomaIncorporacao[0].padraoAcabamento = null;
                        vm.laudoAvaliacao.unidadeAutonomaIncorporacao[0].tipoProjetoCub = null;
                        vm.laudoAvaliacao.unidadeAutonomaIncorporacao[0].estimativas = null;
                        vm.laudoAvaliacao.unidadeAutonomaIncorporacao[0].isFatoresAcrescimo = null;
                    }
                };

                vm.isAlgumaBenfeitoriaPreenchida = function() {
                    for (var i = 0; i < vm.laudoAvaliacao.benfeitoriasImovel.length; i++) {
                        if (vm.laudoAvaliacao.benfeitoriasImovel[i].valor) {
                            return true;
                        }
                    }
                    return false;
                };

                vm.verificarLaudoCadastrado = function () {
                    laudoAvaliacaoService.verificarLaudoCadastrado(vm.laudoAvaliacao.tipoAvaliacao, vm.rip, vm.laudoAvaliacao.dataAssinatura).then(function (resp) {
                        vm.isLaudoCadastrado = resp.data;
                    });
                };

                vm.validarValorImovel = function () {
                    laudoAvaliacaoService.validarValorImovel(vm.laudoAvaliacao);
                };

                vm.labelTipoLaudo = function () {
                    if (vm.laudoAvaliacao.tipoLaudoElaborado === 'LAUDO_AVALIACAO' || vm.laudoAvaliacao.tipoLaudoElaborado === 'LAUDO_AVALIACAO_LOCACAO') {
                        return 'Laudo elaborado';
                    } else {
                        return 'Relatório elaborado';
                    }

                };

                vm.limparDadosArquivosModelo = function () {
                    vm.laudoAvaliacao.arquivosModelo = [];
                };

                var buscarListaOrgaos = function () {
                    usuarioAvaliacaoService.getOrgaos().then(function (resp) {
                        vm.listaOrgaos = resp.data;
                        if (vm.listaOrgaos.length === 1) {
                            vm.laudoAvaliacao.orgao = vm.listaOrgaos[0];
                        }
                        vm.buscarListaUnidades();
                    });
                };

                vm.buscarListaUnidades = function () {
                    usuarioAvaliacaoService.getUnidadesUsuario().then(function (resp) {
                        vm.listaUnidades = resp.data;
                        if (vm.listaUnidades.length === 1) {
                            vm.laudoAvaliacao.unidade = vm.listaUnidades[0];
                        }
                    });
                };

                vm.limparArquivosLaudoReferencia = function () {
                    vm.laudoAvaliacao.arquivosLaudoReferencia = [];
                };

                vm.changeLaudoAvaliacaoDocumentoSei = function () {
                    vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento = null;
                    vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei = null;
                };

                vm.changeLaudoAvaliacaoNumeroSei = function () {
                    vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei = null;
                };

                vm.validarLaudoAvaliacaoNumeroSeiProcessoSei = function () {
                    if (vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei) {
                        documentoSeiService.recuperarProcedimentoSei(vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei, vm.imovel.enderecamento.uf).then(function () {

                        }, function (erro) {
                            vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei = null;
                            ComumFactory.mostrarMensagemError(erro.data.message);
                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('menssagem-numero-processo-sei-invalido'));
                    }
                };

                init();
            }])

})();

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cadastrarLaudoAvaliacaoLocacaoController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "imovelAvaliacaoService", "$state", "formularioAvaliacaoService", "$filter", "$mdDialog", "ComumFactory", "avaliacaoEscopoCompartilhadoService", "numeroExtensoAvaliacaoService", "usuarioAvaliacaoService", function ($scope, laudoAvaliacaoService, $stateParams, imovelAvaliacaoService, $state, formularioAvaliacaoService,
                      $filter, $mdDialog, ComumFactory, avaliacaoEscopoCompartilhadoService, numeroExtensoAvaliacaoService, usuarioAvaliacaoService) {

                var vm = $scope;
                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.nomeCampo = $filter('translate')('laudo-relatorio');
                vm.labelVermelho = {color: 'red'};
                vm.listaTiposAvaliacao = [];

                vm.LAUDO_AVALIACAO = 'LAUDO_AVALIACAO';
                vm.LAUDO_AVALIACAO_LOCACAO = 'LAUDO_AVALIACAO_LOCACAO';
                vm.LAUDO_RELATORIO_VALOR_REFERENCIA = 'LAUDO_RELATORIO_VALOR_REFERENCIA';
                vm.INFORMAR_VALORES_LAUDO = 'INFORMAR_VALORES_LAUDO';
                vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';
                vm.POR_TERCEIROS = 'POR_TERCEIROS';
                vm.PREENCHER_SISTEMA = 'PREENCHER_SISTEMA';
                vm.UPLOAD_ARQUIVO = 'UPLOAD_ARQUIVO';
                vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';
                vm.REFERENCIA_INCRA = 'REFERENCIA_INCRA';
                vm.LAUDO_AVALIACAO_AQUISICAO = 'LAUDO_AVALIACAO_AQUISICAO';
                vm.POR_AQUISICAO = 'Por Aquisição';
                vm.TIPOS_AQUISICAO = [
                    'Compra',
                    'Permuta',
                    'Desapropriação'
                ];
                vm.RURAL = 'Rural';
                vm.BRASIL = 'Brasil';

                var HECTARE = 'HECTARE';

                vm.rip = $stateParams.rip;
                vm.imovel = {};
                vm.detalharLaudoRelatorio = false;

                vm.laudoAvaliacao = {};
                vm.laudoAvaliacao.responsaveis = [];
                vm.laudoAvaliacao.homologadores = [];
                vm.responsavelTecnico = {};
                vm.laudoAvaliacao.tipoValorLaudo = null;


                vm.laudoAvaliacao.valorTotalTerreno = 0;


                vm.valorInvalido = false;

                vm.listaMoedas = [
                    {
                        id: 1,
                        codigo: '001',
                        nome: 'Real',
                        simbolo: 'R$',
                        codigoPais: '55',
                        pais: 'Brasil',
                        dataExclusao: new Date()
                    }, {
                        id: 2,
                        codigo: '002',
                        nome: 'Dolar',
                        simbolo: '$',
                        codigoPais: '00',
                        pais: 'Estados Unidos',
                        dataExclusao: new Date()
                    }
                ];

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == 'false' && vm.usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO_NAO_HABILITADO == 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                var init = function () {
                    vm.laudoAvaliacao.tipoAvaliacao = vm.LAUDO_AVALIACAO_LOCACAO;

                    buscarUsuarioLogado(function () {
                            buscarTiposAvaliacaoLaudo();
                            buscarTiposElaboracaoLaudo();
                            buscarTiposMetodologia();
                            buscarTiposMetodosAvaliacao();
                            buscarTipoNotaTecnicaHomologacao();


                            if (avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao')) {

                                vm.rip = avaliacaoEscopoCompartilhadoService.getObjeto('rip');
                                vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                                vm.laudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao');
                                vm.orgao = avaliacaoEscopoCompartilhadoService.getObjeto('orgao');
                                vm.unidade = avaliacaoEscopoCompartilhadoService.getObjeto('unidade');
                                vm.subunidade = avaliacaoEscopoCompartilhadoService.getObjeto('subunidade');
                                if (vm.laudoAvaliacao.laudoRelatorioAvaliacao) {
                                    if (vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao) {
                                        vm.laudoAvaliacao.valorImovel = vm.validarValorLaudoRelatorio(vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor, vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao);
                                        vm.laudoAvaliacao.tipoValorLaudo = vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.tipoValorLaudo;
                                    }
                                    vm.unidadeMedida = vm.laudoAvaliacao.laudoRelatorioAvaliacao.terrenoImovel.terreno.unidadeMedida === HECTARE ? 'ha' : 'm²';
                                    vm.laudoAvaliacao.valorTerreno = vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.valorTerreno;

                                    vm.laudoAvaliacao.dataAssinatura = vm.laudoAvaliacao.dataAssinatura ? new Date(vm.laudoAvaliacao.dataAssinatura) : new Date(vm.laudoAvaliacao.laudoRelatorioAvaliacao.dataAssinatura);
                                    vm.laudoAvaliacao.dataAssinaturaTemp = vm.laudoAvaliacao.dataAssinatura ? new Date(vm.laudoAvaliacao.dataAssinatura) : new Date(vm.laudoAvaliacao.laudoRelatorioAvaliacao.dataAssinatura);
                                    vm.laudoAvaliacao.responsaveis = vm.laudoAvaliacao.laudoRelatorioAvaliacao.identificacao ? vm.laudoAvaliacao.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos : vm.laudoAvaliacao.responsaveis;
                                }

                                vm.dataTemp = new Date(vm.laudoAvaliacao.dataAssinatura).getTime();
                                vm.laudoAvaliacao.dataAssinatura = undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                                vm.laudoAvaliacao.dataAssinatura = vm.dataTemp ? new Date(vm.dataTemp) : undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = vm.dataTemp ? new Date(vm.dataTemp) : undefined;
                                vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp = vm.laudoAvaliacao.tipoNotaTecnicaHomologacao;

                                avaliacaoEscopoCompartilhadoService.limparEscopo();

                            } else {
                                if ($state.current.name.indexOf("EDITAR_LAUDO_AVALIACAO_LOCACAO") !== -1) {
                                    consultarLaudoAvaliacao();
                                    buscarDadosImovel();
                                } else {
                                    buscarDadosImovel();
                                    laudoAvaliacaoService.getOrganizacao().then(function (response) {
                                        vm.laudoAvaliacao.unidade = response.data.organizacao;
                                    });
                                }
                            }
                        }
                    );
                };

                var consultarLaudoAvaliacao = function () {

                    vm.rip = $stateParams.rip;
                    vm.idLaudo = $stateParams.idLaudo;

                    if (vm.rip && vm.idLaudo) {
                        laudoAvaliacaoService.consultarLaudoAvaliacaoId(vm.idLaudo).then(function (resp) {
                            vm.laudoAvaliacao = resp.data;
                            vm.laudoAvaliacao.valorImovelTemp = vm.laudoAvaliacao.valorImovel;
                            vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp = vm.laudoAvaliacao.tipoNotaTecnicaHomologacao;
                            vm.laudoAvaliacao.moeda = angular.copy(resp.data.moeda);
                            if (vm.laudoAvaliacao.dataAssinatura != null) {
                                vm.laudoAvaliacao.dataAssinatura = new Date(resp.data.dataAssinatura);
                                vm.laudoAvaliacao.dataAssinaturaTemp = new Date(resp.data.dataAssinatura);
                            } else {
                                vm.laudoAvaliacao.dataAssinatura = undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                            }
                        });
                    }
                };

                vm.limparNotaTecnica = function () {
                    vm.laudoAvaliacao.notaTecnicaHomologacao = null;
                };

                vm.limpartipoNotaTecnicaHomologacao = function () {
                    vm.laudoAvaliacao.tipoNotaTecnicaHomologacao = vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp;
                    vm.laudoAvaliacao.notaTecnicaHomologacao = null;
                    vm.laudoAvaliacao.homologacao = null;
                    vm.laudoAvaliacao.arquivosNotaTecnicaHomologacao = null;
                    vm.laudoAvaliacao.notaTecnicaDocumentoSei = {};
                }


                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService.converterNumeroPorExtenso(numero, vm.laudoAvaliacao.moeda.nome.toLowerCase());
                };

                vm.validarValorLaudoRelatorio = function (tipoValor, resultadoAvaliacao) {
                    var VALOR_MEDIO = 'VALOR_MEDIO';
                    var VALOR_MINIMO = 'VALOR_MINIMO';
                    var VALOR_MAXIMO = 'VALOR_MAXIMO';
                    var OUTRO_VALOR = 'OUTRO_VALOR';
                    if (tipoValor == VALOR_MEDIO) {
                        return resultadoAvaliacao.valorMedio;
                    } else if (tipoValor == VALOR_MINIMO) {
                        return resultadoAvaliacao.valorMinimo;
                    } else if (tipoValor == VALOR_MAXIMO) {
                        return resultadoAvaliacao.valorMaximo;
                    } else if (tipoValor == OUTRO_VALOR) {
                        return resultadoAvaliacao.outroValorAdotado;
                    }
                };

                vm.inserirResponsavelTecnico = function () {
                    vm.laudoAvaliacao.responsaveis.push(angular.copy(vm.responsavelTecnico));
                };

                var buscarTiposAvaliacaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposAvaliacaoLaudo().then(function (resp) {
                        vm.listaTiposAvaliacao = resp.data;
                    });
                };

                var buscarTiposElaboracaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposElaboracaoLaudo().then(function (resp) {
                        vm.listaElaboracaoLaudo = resp.data;
                    });
                };

                vm.changeTipoAvaliacao = function () {
                    vm.laudoAvaliacao.tipoLaudoElaborado = null;
                    vm.laudoAvaliacao.tipoMetodologia = null;
                    vm.laudoAvaliacao.tipoMetodoAvaliacao = null;
                    vm.laudoAvaliacao.metodologiaOutro = null;
                }

                var buscarTiposMetodologia = function () {
                    vm.listaTiposMetodologia = {
                        INFERENCIA_ESTATISTICA: "Inferência estatística",
                        ESTIMATIVA_VALORES_TERRENO_BENFEITORIA: "Estimativa de valores de terreno e benfeitoria",
                        GLEBA_URBANIZAVEL: "Gleba Urbanizável",
                        OUTRO: "Outro"
                    }
                };

                var buscarTiposMetodosAvaliacao = function () {
                    vm.listaTiposMetodosAvaliacao = {
                        COMPARATIVO_MERCADO: "Comparativo direto de dados de mercado",
                        EVOLUTIVO: "Evolutivo",
                        INVOLUTIVO: "Involutivo",
                        CAPITALIZACAO_RENDA: "Capitalização da renda"
                    }
                };


                var buscarTipoNotaTecnicaHomologacao = function () {
                    laudoAvaliacaoService.buscarTipoNotaTecnicaHomologacao().then(function (resp) {
                        vm.listaTipoNotaTecnicaHomologacao = resp.data;
                    });
                };


                var buscarDadosImovel = function () {
                    imovelAvaliacaoService.consultarImovelPorRip(vm.rip).then(function (resposta) {
                        vm.imovel = resposta.data;
                        vm.unidadeMedida = (vm.imovel.terreno.geometriaTerrenoCartorial ? vm.imovel.terreno.unidadeMedidaCartorial : vm.imovel.terreno.unidadeMedidaCadastral);
                        vm.unidadeMedida = vm.unidadeMedida == HECTARE ? 'ha' : 'm²';
                        validarCampoMoeda();
                        buscarIncorporacaoImovel();
                    });
                };

                var buscarIncorporacaoImovel = function () {
                    imovelAvaliacaoService.buscarDadosGeometriaIncorporacao(vm.imovel.rip).then(function (resp) {
                        vm.imovel.incorporacao = resp.data;
                    });
                };

                vm.validarCPFInformado = function (cpf) {
                    if (!cpf) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-cpf-invalido'));
                    }
                };

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                vm.validarDataAssinaturaPosteriorDataAtual = function () {
                    if (vm.laudoAvaliacao.dataAssinatura > new Date()) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-assinatura-posterior-data-atual'));
                        vm.laudoAvaliacao.dataAssinatura = undefined;
                        vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                        throw $filter('translate')('mensagem-data-assinatura-posterior-data-atual');
                    }
                };

                vm.validarDataAssinaturaOutroLaudo = function () {
                    if (vm.laudoAvaliacao.dataAssinatura) {
                        return laudoAvaliacaoService.validarDataAssinaturaCadastrada(
                            vm.laudoAvaliacao.tipoAvaliacao,
                            vm.laudoAvaliacao.dataAssinatura,
                            vm.rip,
                            vm.laudoAvaliacao.id || null
                        ).then(function () {
                            return Promise.resolve();
                        }).catch(function () {
                            if (
                                vm.laudoAvaliacao.dataAssinatura == undefined || vm.laudoAvaliacao.dataAssinatura == null ||
                                vm.laudoAvaliacao.dataAssinaturaTemp == undefined || vm.laudoAvaliacao.dataAssinaturaTemp == null
                            ) {
                                return;
                            }
                            vm.laudoAvaliacao.dataAssinatura = null;
                            vm.laudoAvaliacao.dataAssinaturaTemp = null;
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-assinatura-outro-laudo'));
                            return Promise.reject();
                        });
                    }
                
                    return Promise.resolve();
                };

                var validarCampoMoeda = function () {
                    if (vm.imovel.enderecamento) {
                        if (vm.imovel.enderecamento.pais == vm.BRASIL) {
                            vm.laudoAvaliacao.moeda = vm.listaMoedas[0];
                        }
                    }
                };

                var preencherDadosLaudoAvaliacao = function () {
                    vm.laudoAvaliacao.ripImovel = vm.rip;
                };


                var validarCreaHomologadoresInformados = function () {
                    if (vm.laudoAvaliacao.isNaoHomologacao === true || vm.laudoAvaliacao.tipoLaudoElaborado != 'POR_TERCEIROS') {
                        return false;
                    }
                    if (verificaResponsaveisHomologadoresIguais()) {
                        return true;
                    }
                    var creaPendente = false;
                    if (vm.laudoAvaliacao.homologadores.length === 0) {
                        creaPendente = true;
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-necessario-informar-homologador'));
                    }

                    return creaPendente;
                };

                var verificaResponsaveisHomologadoresIguais = function () {
                    if (vm.laudoAvaliacao.responsaveis.length > 0) {
                        for (var i = 0; i < vm.laudoAvaliacao.homologadores.length; i++) {
                            for (var j = 0; j < vm.laudoAvaliacao.responsaveis.length; j++) {
                                if (vm.laudoAvaliacao.homologadores[i].cpf === vm.laudoAvaliacao.responsaveis[j].cpf) {
                                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-mesmo-responsavel-homologador'));
                                    return true;
                                }
                            }
                        }
                    }
                    return false;
                };

                vm.verificarHomologador = function () {
                    for (var i in vm.laudoAvaliacao.homologadores) {
                        if (vm.laudoAvaliacao.homologadores[i]) {
                            if (vm.laudoAvaliacao.homologadores[i].cpf === vm.usuarioLogado.cpf) {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                vm.salvarLaudoAvaliacao = function (formularioDadosLaudo) {

                    if (formularioDadosLaudo) {
                        vm.validarFormulario(formularioDadosLaudo);
                        if (!formularioDadosLaudo.$valid || validarCreaHomologadoresInformados()) {
                            return;
                        }
                    }

                    if (vm.laudoAvaliacao.notaTecnicaHomologacao && !vm.laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-preenchimento-incompleto-nota-tenica'));
                        return;
                    }

                    vm.laudoAvaliacao.situacaoLaudo = null;
                    preencherDadosLaudoAvaliacao();
                    vm.validarDataAssinaturaPosteriorDataAtual();
                    validarCampoMoeda();
                    
                    vm.laudoAvaliacao.ufImovel = angular.copy(vm.imovel.enderecamento.uf);
                    laudoAvaliacaoService.verificarLaudoCadastrado(vm.laudoAvaliacao.tipoAvaliacao, vm.rip, vm.laudoAvaliacao.dataAssinatura, vm.laudoAvaliacao.idLaudo).then(function (resp) {
                        vm.isLaudoCadastrado = resp.data;
                        if (vm.isLaudoCadastrado) {
                            var confirm = $mdDialog.confirm()
                                .title("Atenção!")
                                .textContent("Após a alteração desse campo, os valores de Terreno e Benfeitoria deverão ser preenchidos novamente. Deseja continuar?")
                                .ok("Confirmar")
                                .cancel("Cancelar");

                            $mdDialog.show(confirm).then(function () {
                                laudoAvaliacaoService.salvar(vm.laudoAvaliacao).then(function (resp) {
                                    vm.laudoAvaliacao = resp.data;
                                    if (vm.laudoAvaliacao.tipoNotaTecnicaHomologacao === vm.PREENCHER_SISTEMA && vm.laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento && vm.verificarHomologador()) {
                                        laudoAvaliacaoService.assinarLaudoAvaliacaoNotaTecnica(vm.laudoAvaliacao.id).then(function () {
                                        });
                                    }
                                    ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                        ripDetalhar: vm.laudoAvaliacao.ripImovel
                                    });
                                });
                            }, function () {
                                vm.laudoAvaliacao.dataAssinaturaTemp = vm.laudoAvaliacao.dataAssinatura;
                                vm.laudoAvaliacao.valorImovelTemp = vm.laudoAvaliacao.valorImovel;
                            });

                        } else {
                            laudoAvaliacaoService.salvar(vm.laudoAvaliacao).then(function (resp) {
                                vm.laudoAvaliacao = resp.data;
                                if (vm.laudoAvaliacao.tipoNotaTecnicaHomologacao === vm.PREENCHER_SISTEMA && vm.laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento && vm.verificarHomologador()) {
                                    laudoAvaliacaoService.assinarLaudoAvaliacaoNotaTecnica(vm.laudoAvaliacao.id).then(function () {
                                    });
                                }
                                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                    ripDetalhar: vm.laudoAvaliacao.ripImovel
                                });
                            });
                        }
                    });

                };

                vm.salvarComoRascunhoLaudoAvaliacao = function () {

                    preencherDadosLaudoAvaliacao();
                    vm.validarDataAssinaturaPosteriorDataAtual();

                    vm.laudoAvaliacao.situacaoLaudo = "EM_PREENCHIMENTO";
                    vm.laudoAvaliacao.ufImovel = angular.copy(vm.imovel.enderecamento.uf);
                    laudoAvaliacaoService.salvar(vm.laudoAvaliacao).then(function (resp) {
                        var confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent($filter('translate')('mensagem-rascunho-salvo-com-sucesso-continuar-preenchimento'))
                            .ok("Sim")
                            .cancel("Não");

                        $mdDialog.show(confirm).then(function () {
                            vm.laudoAvaliacao = resp.data;
                            vm.laudoAvaliacao.valorImovelTemp = vm.laudoAvaliacao.valorImovel;
                            vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp = vm.laudoAvaliacao.tipoNotaTecnicaHomologacao;
                            vm.laudoAvaliacao.moeda = angular.copy(resp.data.moeda);
                            if (vm.laudoAvaliacao.dataAssinatura != null) {
                                vm.laudoAvaliacao.dataAssinatura = new Date(resp.data.dataAssinatura);
                                vm.laudoAvaliacao.dataAssinaturaTemp = new Date(resp.data.dataAssinatura);
                            } else {
                                vm.laudoAvaliacao.dataAssinatura = undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                            }
                        }, function () {
                            $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                ripDetalhar: vm.rip
                            });
                        });
                    });
                };

                vm.inserirNotaTecnicaHomologacao = function () {
                    preencherRipLaudoEscopoCompartilhado();
                    $state.go('avaliacao.INSERIR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO');
                };

                var preencherRipLaudoEscopoCompartilhado = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriasImovel', vm.listaBenfeitoriasImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaImovel', vm.laudoAvaliacao.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoValorLaudo', vm.laudoAvaliacao.tipoValorLaudo);
                };

                vm.changeValoresDataValor = function () {
                    vm.laudoAvaliacao.dataAssinatura = vm.laudoAvaliacao.dataAssinaturaTemp;
                    vm.laudoAvaliacao.valorImovel = vm.laudoAvaliacao.valorImovelTemp;
                    vm.validarDataAssinaturaOutroLaudo();
                    vm.validarDataAssinaturaPosteriorDataAtual();
                };

                vm.verificarLaudoCadastrado = function () {
                    laudoAvaliacaoService.verificarLaudoCadastrado(vm.laudoAvaliacao.tipoAvaliacao, vm.rip, vm.laudoAvaliacao.dataAssinatura).then(function (resp) {
                        vm.isLaudoCadastrado = resp.data;
                    });
                };


                vm.validarValorImovel = function () {
                    if (vm.laudoAvaliacao.tipoValorLaudo == vm.INFORMAR_VALORES_LAUDO) {
                        if (!vm.laudoAvaliacao.valorImovel) {
                            vm.laudoAvaliacao.valorImovel = 0;
                        }
                        if (vm.laudoAvaliacao.valorTerreno > vm.laudoAvaliacao.valorImovel || vm.laudoAvaliacao.valorTerreno < 0) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                            vm.laudoAvaliacao.valorTerreno = null;
                            for (i = 0; i < vm.laudoAvaliacao.benfeitoriasImovel.length; i++) {
                                vm.laudoAvaliacao.benfeitoriasImovel[i].valor = 0;
                            }
                            return;
                        }
                    }
                };

                vm.limparDadosHomologacao = function () {
                    vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp = null;
                    vm.laudoAvaliacao.notaTecnicaHomologacao = null;
                    vm.laudoAvaliacao.homologacao = null;
                    vm.laudoAvaliacao.arquivosNotaTecnicaHomologacao = null;
                    vm.laudoAvaliacao.tipoNotaTecnicaHomologacao = null;
                    vm.laudoAvaliacao.homologadores = null;
                    vm.laudoAvaliacao.justificativaNaoHomologacao = null;
                }


                init();
            }])

})
();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cadastrarLaudoAvaliacaoController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "imovelAvaliacaoService", "$state", "formularioAvaliacaoService", "avaliacaoLaudoService", "$filter", "$mdDialog", "ComumFactory", "avaliacaoEscopoCompartilhadoService", "numeroExtensoAvaliacaoService", "usuarioAvaliacaoService", "documentoSeiService", function ($scope, laudoAvaliacaoService, $stateParams, imovelAvaliacaoService, $state, formularioAvaliacaoService, avaliacaoLaudoService,
                      $filter, $mdDialog, ComumFactory, avaliacaoEscopoCompartilhadoService, numeroExtensoAvaliacaoService, usuarioAvaliacaoService, documentoSeiService) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.nomeCampo = $filter('translate')('laudo-relatorio');
                vm.labelVermelho = {color: 'red'};
                vm.listaTiposAvaliacao = [];
                vm.listaBenfeitoriasImovel = [];
                vm.unidadeAutonomaImovel = null;

                vm.LAUDO_AVALIACAO = 'LAUDO_AVALIACAO';
                vm.LAUDO_RELATORIO_VALOR_REFERENCIA = 'LAUDO_RELATORIO_VALOR_REFERENCIA';
                vm.INFORMAR_VALORES_LAUDO = 'INFORMAR_VALORES_LAUDO';
                vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';
                vm.POR_TERCEIROS = 'POR_TERCEIROS';
                vm.PREENCHER_SISTEMA = 'PREENCHER_SISTEMA';
                vm.UPLOAD_ARQUIVO = 'UPLOAD_ARQUIVO';
                vm.DOCUMENTO_SEI = 'DOCUMENTO_SEI';
                vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';
                vm.REFERENCIA_INCRA = 'REFERENCIA_INCRA';
                vm.LAUDO_AVALIACAO_AQUISICAO = 'LAUDO_AVALIACAO_AQUISICAO';
                vm.POR_AQUISICAO = 'Por Aquisição';
                vm.TIPOS_AQUISICAO = [
                    'Compra',
                    'Permuta',
                    'Desapropriação'
                ];
                vm.RURAL = 'Rural';
                vm.BRASIL = 'Brasil';

                var HECTARE = 'HECTARE';

                vm.rip = $stateParams.rip;
                vm.imovel = {};
                vm.detalharLaudoRelatorio = false;

                vm.laudoAvaliacao = {};
                vm.laudoAvaliacao.responsaveis = [];
                vm.laudoAvaliacao.homologadores = [];
                vm.responsavelTecnico = {};
                vm.laudoAvaliacao.tipoValorLaudo = null;
                vm.laudoAvaliacao.valorTotalTerreno = 0;
                vm.valorInvalido = false;

                vm.listaMoedas = [
                    {
                        id: 1,
                        codigo: '001',
                        nome: 'Real',
                        simbolo: 'R$',
                        codigoPais: '55',
                        pais: 'Brasil',
                        dataExclusao: new Date()
                    }, {
                        id: 2,
                        codigo: '002',
                        nome: 'Dolar',
                        simbolo: '$',
                        codigoPais: '00',
                        pais: 'Estados Unidos',
                        dataExclusao: new Date()
                    }
                ];

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO === 'false' && vm.usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO_NAO_HABILITADO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                var init = function () {
                    vm.laudoAvaliacao.tipoAvaliacao = vm.LAUDO_AVALIACAO;

                    buscarUsuarioLogado(function () {
                        //buscarListaOrgaos();
                        vm.buscarListaUnidades();
                        buscarTiposAvaliacaoLaudo();
                        buscarTiposMetodologia();
                        buscarTiposMetodosAvaliacao();
                        buscarFonteValorTerreno();
                        buscarTipoNotaTecnicaHomologacao();
                        vm.buscarTiposElaboracaoLaudo();
                        buscarTiposValores();

                        if (avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao')) {
                            vm.rip = avaliacaoEscopoCompartilhadoService.getObjeto('rip');
                            vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                            vm.laudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao');
                            vm.orgao = avaliacaoEscopoCompartilhadoService.getObjeto('orgao');
                            vm.unidade = avaliacaoEscopoCompartilhadoService.getObjeto('unidade');
                            vm.subunidade = avaliacaoEscopoCompartilhadoService.getObjeto('subunidade');
                            if (vm.laudoAvaliacao.laudoRelatorioAvaliacao) {
                                if (vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao) {
                                    vm.laudoAvaliacao.valorImovel = vm.validarValorLaudoRelatorio(vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor, vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao);
                                    vm.laudoAvaliacao.tipoValorLaudo = vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.tipoValorLaudo;
                                }
                                vm.unidadeMedida = vm.laudoAvaliacao.laudoRelatorioAvaliacao.terrenoImovel.terreno.unidadeMedida === HECTARE ? 'ha' : 'm²';
                                vm.laudoAvaliacao.valorTerreno = vm.laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.valorTerreno;
                                vm.laudoAvaliacao.unidadeAutonomaIncorporacao = vm.laudoAvaliacao.laudoRelatorioAvaliacao.unidadeAutonoma.unidadeAutonomaIncorporacao;
                                var benfeitoriasImovel = [];
                                angular.forEach(vm.laudoAvaliacao.laudoRelatorioAvaliacao.benfeitorias.benfeitoriasAvaliacao, function (benfeitoriasAvaliacao) {
                                    benfeitoriasImovel.push(benfeitoriasAvaliacao.benfeitoriaImovel);
                                });
                                vm.laudoAvaliacao.benfeitoriasImovel = benfeitoriasImovel;
                                vm.laudoAvaliacao.dataAssinatura = vm.laudoAvaliacao.dataAssinatura ? new Date(vm.laudoAvaliacao.dataAssinatura) : new Date(vm.laudoAvaliacao.laudoRelatorioAvaliacao.dataAssinatura);
                                vm.laudoAvaliacao.dataAssinaturaTemp = vm.laudoAvaliacao.dataAssinatura ? new Date(vm.laudoAvaliacao.dataAssinatura) : new Date(vm.laudoAvaliacao.laudoRelatorioAvaliacao.dataAssinatura);
                                vm.laudoAvaliacao.responsaveis = vm.laudoAvaliacao.laudoRelatorioAvaliacao.identificacao ? vm.laudoAvaliacao.laudoRelatorioAvaliacao.identificacao.responsaveisTecnicos : vm.laudoAvaliacao.responsaveis;
                            }

                            vm.dataTemp = new Date(vm.laudoAvaliacao.dataAssinatura).getTime();
                            vm.laudoAvaliacao.dataAssinatura = undefined;
                            vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                            vm.laudoAvaliacao.dataAssinatura = vm.dataTemp ? new Date(vm.dataTemp) : undefined;
                            vm.laudoAvaliacao.dataAssinaturaTemp = vm.dataTemp ? new Date(vm.dataTemp) : undefined;
                            vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp = vm.laudoAvaliacao.tipoNotaTecnicaHomologacao;
                            vm.laudoAvaliacao.valorAdotadoTemp = vm.laudoAvaliacao.valorAdotado;
                            vm.laudoAvaliacao.outroValorAdotadoTemp = vm.laudoAvaliacao.outroValorAdotado;

                            definirAreaImovel();
                            avaliacaoEscopoCompartilhadoService.limparEscopo();

                        } else {
                            buscarDadosImovel();
                            if ($state.current.name.indexOf("EDITAR_LAUDO_AVALIACAO") !== -1) {
                                consultarLaudoAvaliacao();
                            } else {
                                buscarListaBenfeitoriasSu();
                                buscarDadosUnidadeAutonomaIncorporacao();
                            }
                        }
                    });
                };

                var consultarLaudoAvaliacao = function () {

                    vm.rip = $stateParams.rip;
                    vm.idLaudo = $stateParams.idLaudo;

                    if (vm.rip && vm.idLaudo) {
                        laudoAvaliacaoService.consultarLaudoAvaliacaoId(vm.idLaudo).then(function (resp) {
                            vm.laudoAvaliacao = resp.data;

                            if (vm.laudoAvaliacao.benfeitoriasImovel) {
                                vm.laudoAvaliacao.benfeitoriasImovel.forEach(function (b) {
                                    if (b.valorCub != null) {
                                        b.benfeitoriaImovel = b.benfeitoriaImovel || {};
                                        b.benfeitoriaImovel.cubDepreciado = b.valorCub;
                                    }
                                });
                            }

                            vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp = vm.laudoAvaliacao.tipoNotaTecnicaHomologacao;
                            vm.laudoAvaliacao.moeda = angular.copy(resp.data.moeda);
                            vm.laudoAvaliacao.valorAdotadoTemp = vm.laudoAvaliacao.valorAdotado;
                            vm.laudoAvaliacao.outroValorAdotadoTemp = vm.laudoAvaliacao.outroValorAdotado;
                            if (vm.laudoAvaliacao.dataAssinatura != null) {
                                vm.laudoAvaliacao.dataAssinatura = new Date(resp.data.dataAssinatura);
                                vm.laudoAvaliacao.dataAssinaturaTemp = new Date(resp.data.dataAssinatura);
                            } else {
                                vm.laudoAvaliacao.dataAssinatura = undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                            }
                            if (!vm.laudoAvaliacao.benfeitoriasImovel) {
                                buscarListaBenfeitoriasSu();
                            }

                            vm.buscarListaUnidades();
                        });
                    }
                };

                vm.limparNotaTecnica = function () {
                    vm.laudoAvaliacao.notaTecnicaHomologacao = null;
                };

                vm.ripImovelLink = function (rip) {
                    laudoAvaliacaoService.navigateToImovel(rip);
                };

                var verificarListaHomologadores = function (callback) {
                    vm.listCpfshomologadoresRemovidos = "";
                    vm.homologadoresSistema = [];
                    vm.homologadoresRemovidos = [];
                    vm.index = [];
                    angular.forEach(vm.laudoAvaliacao.homologadores, function (homologador, index) {
                        usuarioAvaliacaoService.getUsuarioSistema(homologador.cpf).then(function (resp) {
                            vm.index.pop();
                            if (resp.data === "") {
                                vm.listCpfshomologadoresRemovidos += $filter('cpfcnpj')(homologador.cpf) + "<br>";
                                vm.homologadoresRemovidos.push(homologador);
                            } else {
                                vm.homologadoresSistema.push(homologador);
                            }

                            if (vm.index.length === 0) {
                                if (callback) {
                                    callback();
                                }
                            }
                        });

                        vm.index.push(index);
                    });
                };

                vm.limpartipoNotaTecnicaHomologacao = function () {
                    if (vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp === vm.PREENCHER_SISTEMA && vm.laudoAvaliacao.homologadores.length > 0) {
                        verificarListaHomologadores(function () {
                            if (vm.homologadoresRemovidos.length > 0) {
                                var confirm = $mdDialog.confirm()
                                    .title("Atenção!")
                                    .htmlContent($filter('translate')('mensagem-responsaveis-nota-tecnica-homologacao-removidos-nao-usuarios-sistema') + "<br>" +
                                        vm.listCpfshomologadoresRemovidos +
                                        $filter('translate')('mensagem-deseja-alterar-nota-tecnica-homologacao'))
                                    .ok("Confirmar")
                                    .cancel("Cancelar");

                                $mdDialog.show(confirm).then(function () {
                                    vm.laudoAvaliacao.homologadores = angular.copy(vm.homologadoresSistema);
                                    vm.laudoAvaliacao.tipoNotaTecnicaHomologacao = vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp;
                                    vm.laudoAvaliacao.notaTecnicaHomologacao = null;
                                    vm.laudoAvaliacao.homologacao = null;
                                    vm.laudoAvaliacao.arquivosNotaTecnicaHomologacao = null;
                                    vm.laudoAvaliacao.notaTecnicaDocumentoSei = {};
                                }, function () {
                                    vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp = vm.laudoAvaliacao.tipoNotaTecnicaHomologacao;
                                });
                            } else {
                                vm.laudoAvaliacao.tipoNotaTecnicaHomologacao = vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp;
                                vm.laudoAvaliacao.notaTecnicaHomologacao = null;
                                vm.laudoAvaliacao.homologacao = null;
                                vm.laudoAvaliacao.arquivosNotaTecnicaHomologacao = null;
                                vm.laudoAvaliacao.notaTecnicaDocumentoSei = {};
                            }
                        });
                    } else {
                        vm.laudoAvaliacao.tipoNotaTecnicaHomologacao = vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp;
                        vm.laudoAvaliacao.notaTecnicaHomologacao = null;
                        vm.laudoAvaliacao.homologacao = null;
                        vm.laudoAvaliacao.arquivosNotaTecnicaHomologacao = null;
                        vm.laudoAvaliacao.notaTecnicaDocumentoSei = {};
                    }
                };

                vm.limparDadosHomologacao = function () {
                    vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp = null;
                    vm.laudoAvaliacao.notaTecnicaHomologacao = null;
                    vm.laudoAvaliacao.homologacao = null;
                    vm.laudoAvaliacao.arquivosNotaTecnicaHomologacao = null;
                    vm.laudoAvaliacao.tipoNotaTecnicaHomologacao = null;
                    vm.laudoAvaliacao.homologadores = null;
                    vm.laudoAvaliacao.justificativaNaoHomologacao = null;
                };

                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService.converterNumeroPorExtenso(numero, vm.laudoAvaliacao.moeda.nome.toLowerCase());
                };

                vm.validarValorLaudoRelatorio = function (tipoValor, resultadoAvaliacao) {
                    var VALOR_MEDIO = 'VALOR_MEDIO';
                    var VALOR_MINIMO = 'VALOR_MINIMO';
                    var VALOR_MAXIMO = 'VALOR_MAXIMO';
                    var OUTRO_VALOR = 'OUTRO_VALOR';
                    if (tipoValor === VALOR_MEDIO) {
                        return resultadoAvaliacao.valorMedio;
                    } else if (tipoValor === VALOR_MINIMO) {
                        return resultadoAvaliacao.valorMinimo;
                    } else if (tipoValor === VALOR_MAXIMO) {
                        return resultadoAvaliacao.valorMaximo;
                    } else if (tipoValor === OUTRO_VALOR) {
                        return resultadoAvaliacao.outroValorAdotado;
                    }
                };

                vm.inserirResponsavelTecnico = function () {
                    vm.laudoAvaliacao.responsaveis.push(angular.copy(vm.responsavelTecnico));
                };

                var buscarTiposAvaliacaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposAvaliacaoLaudo().then(function (resp) {
                        vm.listaTiposAvaliacao = resp.data;
                    });
                };

                vm.buscarTiposElaboracaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposElaboracaoLaudo().then(function (resp) {
                        vm.listaElaboracaoLaudo = resp.data;
                    });
                };

                vm.changeTipoAvaliacao = function () {
                    vm.laudoAvaliacao.tipoLaudoElaborado = null;
                    vm.laudoAvaliacao.tipoMetodologia = null;
                    vm.laudoAvaliacao.tipoMetodoAvaliacao = null;
                    vm.laudoAvaliacao.metodologiaOutro = null;
                }

                var buscarTiposMetodologia = function () {
                    vm.listaTiposMetodologia = {
                        INFERENCIA_ESTATISTICA: "Inferência estatística",
                        ESTIMATIVA_VALORES_TERRENO_BENFEITORIA: "Estimativa de valores de terreno e benfeitoria",
                        GLEBA_URBANIZAVEL: "Gleba Urbanizável",
                        OUTRO: "Outro"
                    }
                };

                var buscarTiposMetodosAvaliacao = function () {
                    vm.listaTiposMetodosAvaliacao = {
                        COMPARATIVO_MERCADO: "Comparativo direto de dados de mercado",
                        EVOLUTIVO: "Evolutivo",
                        INVOLUTIVO: "Involutivo",
                        CAPITALIZACAO_RENDA: "Capitalização da renda"
                    }
                };

                var buscarTipoNotaTecnicaHomologacao = function () {
                    vm.listaTipoNotaTecnicaHomologacao = {
                        UPLOAD_ARQUIVO: "Upload de Arquivo (PDF)",
                        PREENCHER_SISTEMA: "Preencher no Sistema",
                        DOCUMENTO_SEI: "Utilizar Documento SEI"

                    }
                };

                var buscarFonteValorTerreno = function () {
                    laudoAvaliacaoService.buscarFonteValorTerreno().then(function (resp) {
                        vm.listaFonteValorTerreno = resp.data;
                    });
                };

                var buscarDadosImovel = function () {
                    imovelAvaliacaoService.consultarImovelPorRip(vm.rip).then(function (resposta) {
                        vm.imovel = resposta.data;
                        vm.unidadeMedida = (vm.imovel.terreno.geometriaTerrenoCartorial ? vm.imovel.terreno.unidadeMedidaCartorial : vm.imovel.terreno.unidadeMedidaCadastral);
                        vm.unidadeMedida = vm.unidadeMedida === HECTARE ? 'ha' : 'm²';
                        validarCampoMoeda();
                        buscarIncorporacaoImovel();
                        definirAreaImovel();
                    });
                };

                var definirAreaImovel = function () {
                    vm.areaTerrenoImovel = vm.imovel.terreno.areaTotalCartorial !== 0 ? vm.imovel.terreno.areaTotalCartorial : vm.imovel.terreno.areaTotalCadastral;
                };

                var buscarIncorporacaoImovel = function () {
                    imovelAvaliacaoService.buscarDadosGeometriaIncorporacao(vm.imovel.rip).then(function (resp) {
                        vm.imovel.incorporacao = resp.data;
                    });
                };

                var buscarListaBenfeitoriasSu = function () {
                    if (vm.rip) {
                        imovelAvaliacaoService.buscarBenfeitoriasSuPorRip(vm.rip).then(function (resp) {
                            vm.laudoAvaliacao.benfeitoriasImovel = resp.data;
                        });
                    }
                };

                vm.exibirInserirValoresTerrenoBenfeitoria = function () {
                    return (vm.laudoAvaliacao.benfeitoriasImovel && vm.laudoAvaliacao.benfeitoriasImovel.length > 0)
                    || (vm.laudoAvaliacao.unidadeAutonomaIncorporacao && vm.laudoAvaliacao.unidadeAutonomaIncorporacao.length > 0);
                };

                var buscarDadosUnidadeAutonomaIncorporacao = function () {
                    if (vm.rip) {
                        imovelAvaliacaoService.buscarUnidadeAutonomaSuPorRip(vm.rip).then(function (resp) {
                            if (resp.data === "") {
                                vm.laudoAvaliacao.unidadeAutonomaIncorporacao = [];
                            } else {
                                vm.laudoAvaliacao.unidadeAutonomaIncorporacao = [];
                                vm.laudoAvaliacao.unidadeAutonomaIncorporacao.push(resp.data);
                            }
                        });
                    }
                };

                vm.validarCPFInformado = function (cpf) {
                    if (!cpf) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-cpf-invalido'));
                    }
                };

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                vm.validarDataAssinaturaPosteriorDataAtual = function () {
                    if (vm.laudoAvaliacao.dataAssinatura && vm.laudoAvaliacao.dataAssinatura > new Date()) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-assinatura-posterior-data-atual'));
                        vm.laudoAvaliacao.dataAssinatura = undefined;
                        vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                        throw $filter('translate')('mensagem-data-assinatura-posterior-data-atual');
                    }
                };

                vm.validarDataAssinaturaOutroLaudo = function () {
                    if (vm.laudoAvaliacao.dataAssinatura) {
                        return laudoAvaliacaoService.validarDataAssinaturaCadastrada(
                            vm.laudoAvaliacao.tipoAvaliacao,
                            vm.laudoAvaliacao.dataAssinatura,
                            vm.rip,
                            vm.laudoAvaliacao.id || null
                        ).then(function () {
                            return Promise.resolve();
                        }).catch(function () {
                            if (
                                vm.laudoAvaliacao.dataAssinatura == undefined || vm.laudoAvaliacao.dataAssinatura == null ||
                                vm.laudoAvaliacao.dataAssinaturaTemp == undefined || vm.laudoAvaliacao.dataAssinaturaTemp == null
                            ) {
                                return;
                            }
                            vm.laudoAvaliacao.dataAssinatura = null;
                            vm.laudoAvaliacao.dataAssinaturaTemp = null;
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-assinatura-outro-laudo'));
                            return Promise.reject();
                        });
                    }

                    return Promise.resolve();
                };

                var validarCampoMoeda = function () {
                    if (vm.imovel.enderecamento) {
                        if (vm.imovel.enderecamento.pais === vm.BRASIL) {
                            vm.laudoAvaliacao.moeda = vm.listaMoedas[0];
                        }
                    }
                };

                var preencherDadosLaudoAvaliacao = function () {
                    vm.laudoAvaliacao.ripImovel = vm.rip;
                };

                var validarCreaHomologadoresInformados = function () {
                    if (vm.laudoAvaliacao.isNaoHomologacao === true || vm.laudoAvaliacao.tipoLaudoElaborado !== 'POR_TERCEIROS') {
                        return false;
                    }
                    if (verificaResponsaveisHomologadoresIguais()) {
                        return true;
                    }
                    var creaPendente = false;
                    if (vm.laudoAvaliacao.homologadores.length === 0) {
                        creaPendente = true;
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-necessario-informar-homologador'));
                    }

                    return creaPendente;
                };

                var verificaResponsaveisHomologadoresIguais = function () {
                    if (vm.laudoAvaliacao.responsaveis.length > 0) {
                        for (var i = 0; i < vm.laudoAvaliacao.homologadores.length; i++) {
                            for (var j = 0; j < vm.laudoAvaliacao.responsaveis.length; j++) {
                                if (vm.laudoAvaliacao.homologadores[i].cpf === vm.laudoAvaliacao.responsaveis[j].cpf) {
                                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-mesmo-responsavel-homologador'));
                                    return true;
                                }
                            }
                        }
                    }
                    return false;
                };

                vm.verificarHomologador = function () {
                    for (var i in vm.laudoAvaliacao.homologadores) {
                        if (vm.laudoAvaliacao.homologadores[i]) {
                            if (vm.laudoAvaliacao.homologadores[i].cpf === vm.usuarioLogado.cpf) {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                vm.valoresBenfeitoriasPreenchidos = function () {
                    if (vm.laudoAvaliacao.benfeitoriasImovel){
                        for (var i = 0; i < vm.laudoAvaliacao.benfeitoriasImovel.length; i++) {
                            if (vm.laudoAvaliacao.benfeitoriasImovel[i].valor == null) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-benfeitoria-nulo'));
                                return false;
                            }
                        };
                    }
                    return true;
                }

                vm.salvarLaudoAvaliacao = function (formularioDadosLaudo) {

                    if (!vm.valoresBenfeitoriasPreenchidos()){
                        return;
                    }

                    if (formularioDadosLaudo) {
                        vm.validarFormulario(formularioDadosLaudo);
                        if (!formularioDadosLaudo.$valid) {
                            return;
                        }
                    }

                    if (angular.isDefined(vm.laudoAvaliacao.valorImovel) && angular.isDefined(vm.laudoAvaliacao.valorTerreno) && angular.isDefined(vm.laudoAvaliacao.valorTotalTerreno)) {
                        if (vm.laudoAvaliacao.tipoValorLaudo === vm.INFORMAR_VALORES_LAUDO) {
                            if (vm.laudoAvaliacao.valorTerreno > vm.laudoAvaliacao.valorImovel) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-are-construida-bao-corresponde-valor-imovel'));
                                return;
                            }
                        }
                    }

                    if (vm.laudoAvaliacao.benfeitoriasImovel && vm.laudoAvaliacao.benfeitoriasImovel.length > 0) {

                        //TODO Verificar porque no cadastro não obriga o valor do CUB...
                        //DESABILITADO IB 4734672
                        /*for (var i = 0; i < vm.laudoAvaliacao.benfeitoriasImovel.length; i++) {
                            if (vm.laudoAvaliacao.benfeitoriasImovel[i].tipoBenfeitoria.codigoSistemico === 'INC_FORM_BENFEITORIA_EDIFICAC' && (vm.laudoAvaliacao.benfeitoriasImovel[i].padraoAcabamento === null || vm.laudoAvaliacao.benfeitoriasImovel[i].tipoProjetoCub === null)) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-cub'));
                                return;
                            }
                        }*/

                        for (var i = 0; i < vm.laudoAvaliacao.benfeitoriasImovel.length; i++) {
                            if (vm.laudoAvaliacao.benfeitoriasImovel[i].edificacao) {
                                vm.laudoAvaliacao.benfeitoriasImovel[i].edificacao.documentacao = null;
                            }
                        }
                    }

                    if (vm.laudoAvaliacao.notaTecnicaHomologacao && !vm.laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-preenchimento-incompleto-nota-tenica'));
                        return;
                    }

                    vm.validarValorImovel();

                    if (vm.laudoAvaliacao.responsaveis.length == 0) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-adicionar-responsavel-tecnico'));
                        return;
                    }

                    if (vm.laudoAvaliacao.arquivosLaudoReferencia.length == 0) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-adicionar-laudo-avaliacao'));
                        return;
                    }

                    if (vm.laudoAvaliacao.valorMinimo && vm.laudoAvaliacao.valorMaximo && vm.laudoAvaliacao.outroValorAdotadoTemp) {
                        if (vm.laudoAvaliacao.outroValorAdotadoTemp > vm.laudoAvaliacao.valorMaximo || vm.laudoAvaliacao.outroValorAdotadoTemp < vm.laudoAvaliacao.valorMinimo) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-adotado-maior-maximo-menor-minimo'));
                            vm.laudoAvaliacao.outroValorAdotadoTemp = null;
                            return;
                        }
                    }

                    vm.laudoAvaliacao.situacaoLaudo = null;
                    preencherDadosLaudoAvaliacao();
                    vm.validarDataAssinaturaPosteriorDataAtual();
                    validarCampoMoeda();

                    vm.laudoAvaliacao.ufImovel = angular.copy(vm.imovel.enderecamento.uf);

                    vm.laudoAvaliacao.benfeitoriasImovel.forEach(function (benfeitoria) {
                        if (benfeitoria.benfeitoriaImovel && benfeitoria.benfeitoriaImovel.cubDepreciado !== undefined) {
                            benfeitoria.cubDepreciado = benfeitoria.benfeitoriaImovel.cubDepreciado;
                        }
                    });

                    laudoAvaliacaoService.salvar(vm.laudoAvaliacao).then(function (resp) {
                        vm.laudoAvaliacao = resp.data;
                        if (vm.laudoAvaliacao.tipoNotaTecnicaHomologacao === vm.PREENCHER_SISTEMA &&
                            vm.laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento) {
                            laudoAvaliacaoService.assinarLaudoAvaliacaoNotaTecnica(vm.laudoAvaliacao.id);
                        }

                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                        $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                            ripDetalhar: vm.laudoAvaliacao.ripImovel
                        });
                    }, function (erro) {
                                ComumFactory.mostrarMensagemError(erro.data.message);
                    });

                };

                vm.salvarComoRascunhoLaudoAvaliacao = function () {

                    if (vm.laudoAvaliacao.benfeitoriasImovel && vm.laudoAvaliacao.benfeitoriasImovel.length > 0) {
                        for (var i = 0; i < vm.laudoAvaliacao.benfeitoriasImovel.length; i++) {
                            if (vm.laudoAvaliacao.benfeitoriasImovel[i].edificacao) {
                                vm.laudoAvaliacao.benfeitoriasImovel[i].edificacao.documentacao = null;
                            }
                        }
                    }

                    vm.validarValorImovel();
                    preencherDadosLaudoAvaliacao();
                    vm.validarDataAssinaturaPosteriorDataAtual();

                    vm.laudoAvaliacao.situacaoLaudo = "EM_PREENCHIMENTO";
                    vm.laudoAvaliacao.ufImovel = angular.copy(vm.imovel.enderecamento.uf);

                    if (vm.laudoAvaliacao.benfeitoriasImovel) {
                        vm.laudoAvaliacao.benfeitoriasImovel.forEach(function (b) {
                            if (b.benfeitoriaImovel && b.benfeitoriaImovel.cubDepreciado !== undefined) {
                                b.benfeitoriaImovel.nu_valor_cub = b.benfeitoriaImovel.cubDepreciado;
                            }
                        });
                    }

                    laudoAvaliacaoService.salvar(vm.laudoAvaliacao).then(function (resp) {
                        var confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent($filter('translate')('mensagem-rascunho-salvo-com-sucesso-continuar-preenchimento'))
                            .ok("Sim")
                            .cancel("Não");

                        $mdDialog.show(confirm).then(function () {
                            vm.laudoAvaliacao = resp.data;
                            vm.laudoAvaliacao.valorImovelTemp = vm.laudoAvaliacao.valorImovel;
                            vm.laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp = vm.laudoAvaliacao.tipoNotaTecnicaHomologacao;
                            vm.laudoAvaliacao.moeda = angular.copy(resp.data.moeda);
                            if (vm.laudoAvaliacao.dataAssinatura != null) {
                                vm.laudoAvaliacao.dataAssinatura = new Date(resp.data.dataAssinatura);
                                vm.laudoAvaliacao.dataAssinaturaTemp = new Date(resp.data.dataAssinatura);
                            } else {
                                vm.laudoAvaliacao.dataAssinatura = undefined;
                                vm.laudoAvaliacao.dataAssinaturaTemp = undefined;
                            }
                            if (!vm.laudoAvaliacao.benfeitoriasImovel) {
                                buscarListaBenfeitoriasSu();
                            }
                            vm.laudoAvaliacao.valorAdotadoTemp = vm.laudoAvaliacao.valorAdotado;
                            vm.laudoAvaliacao.outroValorAdotadoTemp = vm.laudoAvaliacao.outroValorAdotado;
                        }, function () {
                            $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                ripDetalhar: vm.rip
                            });
                        });
                    });
                };

                vm.redirecionarInformarValorCub = function (benfeitoria, tipoTerreno) {
                    preencherRipLaudoEscopoCompartilhado();
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', tipoTerreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', benfeitoria);
                    $state.go('avaliacao.INFORMAR_VALOR_CUB');
                };

                vm.inserirNotaTecnicaHomologacao = function () {
                    preencherRipLaudoEscopoCompartilhado();
                    $state.go('avaliacao.INSERIR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO');
                };

                var preencherRipLaudoEscopoCompartilhado = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriasImovel', vm.listaBenfeitoriasImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaImovel', vm.laudoAvaliacao.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoValorLaudo', vm.laudoAvaliacao.tipoValorLaudo);
                };

                vm.limparDadosUnidadeAutonomaEBenfeitorias = function () {
                    vm.laudoAvaliacao.valorTerreno = null;
                    if (vm.laudoAvaliacao.benfeitoriasImovel && vm.laudoAvaliacao.benfeitoriasImovel.length > 0) {
                        angular.forEach(vm.laudoAvaliacao.benfeitoriasImovel, function (benfeitoria) {
                            benfeitoria.valor = null;
                            benfeitoria.padraoAcabamento = null;
                            benfeitoria.tipoProjetoCub = null;
                            benfeitoria.idadeAparente = null;
                            benfeitoria.vidaUtil = null;
                            benfeitoria.estadoConservacao = null;
                            benfeitoria.valorCub = null;
                            benfeitoria.valorCubDepreciado = null;
                            benfeitoria.estimativas = null;
                            benfeitoria.isFatoresAcrescimo = null;
                        });
                    } else if (vm.laudoAvaliacao.unidadeAutonomaIncorporacao && vm.laudoAvaliacao.unidadeAutonomaIncorporacao.length > 0) {
                        vm.laudoAvaliacao.unidadeAutonomaIncorporacao[0].valor = null;
                        vm.laudoAvaliacao.unidadeAutonomaIncorporacao[0].padraoAcabamento = null;
                        vm.laudoAvaliacao.unidadeAutonomaIncorporacao[0].tipoProjetoCub = null;
                        vm.laudoAvaliacao.unidadeAutonomaIncorporacao[0].estimativas = null;
                        vm.laudoAvaliacao.unidadeAutonomaIncorporacao[0].isFatoresAcrescimo = null;
                    }
                };

                vm.verificarLaudoCadastrado = function () {
                    laudoAvaliacaoService.verificarLaudoCadastrado(vm.laudoAvaliacao.tipoAvaliacao, vm.rip, vm.laudoAvaliacao.dataAssinatura).then(function (resp) {
                        vm.isLaudoCadastrado = resp.data;
                    });
                };

                vm.validarValorImovel = function () {
                    laudoAvaliacaoService.validarValorImovel(vm.laudoAvaliacao);
                };

                vm.limparDadosArquivosModelo = function () {
                    vm.laudoAvaliacao.arquivosModelo = [];
                };

                var buscarListaOrgaos = function () {
                    usuarioAvaliacaoService.getOrgaos().then(function (resp) {
                        vm.listaOrgaos = resp.data;
                        if (vm.listaOrgaos.length === 1) {
                            vm.laudoAvaliacao.orgao = vm.listaOrgaos[0];
                        }
                        vm.buscarListaUnidades();
                    });
                };

                vm.buscarListaUnidades = function () {
                    usuarioAvaliacaoService.getUnidadesUsuario().then(function (resp) {
                        vm.listaUnidades = resp.data;
                        if (vm.listaUnidades.length === 1) {
                            vm.laudoAvaliacao.unidade = vm.listaUnidades[0];
                        }
                    });
                };

                vm.limparArquivosLaudoReferencia = function () {
                    vm.laudoAvaliacao.arquivosLaudoReferencia = [];
                };

                vm.changeLaudoAvaliacaoDocumentoSei = function () {
                    vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento = null;
                    vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei = null;
                    if(vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.isEnviarDocumento && vm.laudoAvaliacao.notaTecnicaDocumentoSei) {
                        vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento = vm.laudoAvaliacao.notaTecnicaDocumentoSei.tipoEnviarDocumento;
                        vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei = vm.laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei;
                    }
                };

                vm.changeNotaTecnicaDocumentoSei = function () {
                    vm.laudoAvaliacao.notaTecnicaDocumentoSei.tipoEnviarDocumento = null;
                    vm.laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei = null;
                    if(vm.laudoAvaliacao.notaTecnicaDocumentoSei.isEnviarDocumento && vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei) {
                        vm.laudoAvaliacao.notaTecnicaDocumentoSei.tipoEnviarDocumento = vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento;
                        vm.laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei = vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei;
                    }
                };

                vm.changeLaudoAvaliacaoNumeroSei = function () {
                    vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei = null;
                    if(vm.laudoAvaliacao.notaTecnicaDocumentoSei) {
                        vm.laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei = null;
                        if (vm.laudoAvaliacao.notaTecnicaDocumentoSei.isEnviarDocumento) {
                            vm.laudoAvaliacao.notaTecnicaDocumentoSei.tipoEnviarDocumento = vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento;

                        }
                    }
                };

                vm.limparNotaTecnicaNumeroSei = function () {
                    vm.laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei = null;
                    if(vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei) {
                        vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei = null;
                        if (vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.isEnviarDocumento) {
                            vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento = vm.laudoAvaliacao.notaTecnicaDocumentoSei.tipoEnviarDocumento;
                        }
                    }
                };

                vm.validarLaudoAvaliacaoNumeroSeiProcessoSei = function () {
                    if (vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei) {
                        documentoSeiService.recuperarProcedimentoSei(vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei, vm.imovel.enderecamento.uf).then(function () {
                            if(vm.laudoAvaliacao.notaTecnicaDocumentoSei) {
                                vm.laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei = vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei;
                            }
                        }, function (erro) {
                            vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei = null;
                            ComumFactory.mostrarMensagemError(erro.data.message);
                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('menssagem-numero-processo-sei-invalido'));
                    }
                };

                vm.validarNotaTecnicaNumeroSeiProcessoSei = function () {
                    documentoSeiService.recuperarProcedimentoSei(vm.laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei, vm.imovel.enderecamento.uf).then(function () {
                        if(vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei) {
                            vm.laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei = vm.laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei;
                        }
                    }, function (erro) {
                        vm.laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei = null;
                        ComumFactory.mostrarMensagemError(erro.data.message);
                    });
                };

                var buscarTiposValores = function () {
                    avaliacaoLaudoService.ordenacaoTipoValor().then(function (resp) {
                        vm.listaTiposValores = resp.data;
                    });
                };

                vm.validarValoresInformados = function () {

                    if (vm.laudoAvaliacao.valorMinimo && vm.laudoAvaliacao.valorMaximo && vm.laudoAvaliacao.outroValorAdotadoTemp) {
                        if (vm.laudoAvaliacao.outroValorAdotadoTemp > vm.laudoAvaliacao.valorMaximo || vm.laudoAvaliacao.outroValorAdotadoTemp < vm.laudoAvaliacao.valorMinimo) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-adotado-maior-maximo-menor-minimo'));
                            vm.laudoAvaliacao.outroValorAdotadoTemp = null;
                            return;
                        }
                    }

                    if (vm.laudoAvaliacao.valorMinimo && vm.laudoAvaliacao.valorMedio) {
                        if (vm.laudoAvaliacao.valorMinimo > vm.laudoAvaliacao.valorMedio) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-mininimo-maior-medio'));
                            vm.laudoAvaliacao.valorMinimo = null;
                            return;
                        }
                    }
                    if (vm.laudoAvaliacao.valorMedio && vm.laudoAvaliacao.valorMaximo) {
                        if (vm.laudoAvaliacao.valorMedio > vm.laudoAvaliacao.valorMaximo) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-médio-maior-maximo'));
                            vm.laudoAvaliacao.valorMedio = null;
                            return;
                        }
                    }

                    if (vm.laudoAvaliacao.valorMinimo && vm.laudoAvaliacao.valorMaximo) {
                        if (vm.laudoAvaliacao.valorMinimo > vm.laudoAvaliacao.valorMaximo) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-mininimo-maior-maximo'));
                            vm.laudoAvaliacao.valorMinimo = null;
                            return;
                        }
                    }

                    if (vm.laudoAvaliacao.valorMinimo || vm.laudoAvaliacao.valorMaximo || vm.laudoAvaliacao.valorMedio) {
                        this.definiValorImovel();
                    }
                };

                vm.changeValoresDataValor = function () {

                    if (vm.laudoAvaliacao.tipoValorLaudo) {
                        var confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent("Após a alteração desse campo, os valores de Terreno e Benfeitoria deverão ser preenchidos novamente. Deseja continuar?")
                            .ok("Confirmar")
                            .cancel("Cancelar");

                        $mdDialog.show(confirm).then(function () {
                            vm.laudoAvaliacao.dataAssinatura = vm.laudoAvaliacao.dataAssinaturaTemp;
                            vm.laudoAvaliacao.valorAdotado = vm.laudoAvaliacao.valorAdotadoTemp;
                            vm.laudoAvaliacao.outroValorAdotado = vm.laudoAvaliacao.outroValorAdotadoTemp;
                            vm.validarDataAssinaturaOutroLaudo();
                            vm.validarDataAssinaturaPosteriorDataAtual();
                            vm.definiValorImovel();
                            vm.limparDadosUnidadeAutonomaEBenfeitorias();
                        }, function () {
                            vm.laudoAvaliacao.dataAssinaturaTemp = vm.laudoAvaliacao.dataAssinatura;
                            vm.laudoAvaliacao.valorAdotadoTemp = vm.laudoAvaliacao.valorAdotado;
                            vm.laudoAvaliacao.outroValorAdotadoTemp = vm.laudoAvaliacao.outroValorAdotado;
                        });
                    } else {
                        vm.laudoAvaliacao.dataAssinatura = vm.laudoAvaliacao.dataAssinaturaTemp;
                        vm.laudoAvaliacao.valorAdotado = vm.laudoAvaliacao.valorAdotadoTemp;
                        vm.laudoAvaliacao.outroValorAdotado = vm.laudoAvaliacao.outroValorAdotadoTemp;
                        vm.validarDataAssinaturaOutroLaudo();
                        vm.validarDataAssinaturaPosteriorDataAtual();
                        vm.definiValorImovel();
                    }

                };

                vm.definiValorImovel = function () {
                    if (vm.laudoAvaliacao.valorAdotado) {
                        switch (vm.laudoAvaliacao.valorAdotado) {
                            case('VALOR_MEDIO'):
                                vm.laudoAvaliacao.valorImovel = vm.laudoAvaliacao.valorMedio;
                                break;
                            case('VALOR_MINIMO'):
                                vm.laudoAvaliacao.valorImovel = vm.laudoAvaliacao.valorMinimo;
                                break;
                            case('VALOR_MAXIMO'):
                                vm.laudoAvaliacao.valorImovel = vm.laudoAvaliacao.valorMaximo;
                                break;
                            case('OUTRO_VALOR'):
                                vm.laudoAvaliacao.valorImovel = vm.laudoAvaliacao.outroValorAdotado;
                                break;
                        }
                    }
                };

                vm.verificarExibicaoDownloadLaudoRevalidacao = function (situacao) {
                    var situacoesNegadas = [
                        'PENDENTE_VALIDACAO_CHEFIA',
                        'EM_PROCESSO_HOMOLOGACAO',
                        'EM_PROCESSO_REVALIDACAO',
                        'REVALIDACAO_PREENCHIMENTO'
                    ];

                    return !situacoesNegadas.includes(situacao);
                };

                vm.verificarExibicaoDocumentoRevalidacaoLaudo = function(situacao) {
                    var situacoesPermitidas = [
                        'PENDENTE_VALIDACAO_CHEFIA',
                        'EM_PROCESSO_HOMOLOGACAO',
                        'EM_PROCESSO_REVALIDACAO',
                        'REVALIDACAO_PREENCHIMENTO'
                    ];

                    return situacoesPermitidas.includes(situacao);
                };

                init();
            }])

})();


(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory("consultarImovelLaudoRelatorioService", ["$http", function($http){
            var URL_DADOS_IMOVEL_LAUDO_RELATORIO = '/avaliacao/api/dadosImovelLaudo';

            var consultarDadosImovelLaudoRelatorio = function(filtroConsulta, offset, limit) {
                return $http.get(URL_DADOS_IMOVEL_LAUDO_RELATORIO + '/consultar/dados', {
                    params: {
                        idPais: filtroConsulta.idPais,
                        cep: filtroConsulta.cep,
                        uf: filtroConsulta.uf,
                        municipio: filtroConsulta.municipio,
                        tipoLogradouro: filtroConsulta.tipoLogradouro,
                        logradouro: filtroConsulta.logradouro,
                        numero: filtroConsulta.numero,
                        bairro: filtroConsulta.bairro,
                        cidadeExterior: filtroConsulta.cidadeExterior,
                        codigoPostal: filtroConsulta.codigoPostal,
                        endereco: filtroConsulta.endereco,
                        offset: offset,
                        limit: limit
                    }
                });
            };

            return {
                consultarDadosImovelLaudoRelatorio: consultarDadosImovelLaudoRelatorio
            }
        }])
})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('consultarImovelLaudoController',
            ["$scope", "consultarImovelLaudoRelatorioService", "dominioAvaliacaoService", "ComumFactory", "$state", "$filter", "usuarioAvaliacaoService", "$mdMedia", "$mdDialog", "imovelAvaliacaoService", "consultarAvaliacaoService", "avaliacaoEscopoCompartilhadoService", function ($scope, consultarImovelLaudoRelatorioService, dominioAvaliacaoService, ComumFactory,
                      $state, $filter, usuarioAvaliacaoService, $mdMedia, $mdDialog, imovelAvaliacaoService, consultarAvaliacaoService, avaliacaoEscopoCompartilhadoService) {

                var vm = $scope;

                vm.idBrasil = null;
                vm.filtroConsulta = {};

                vm.listaDadosImoveisLaudoRelatorio = {};
                vm.listaPaises = [];
                vm.listaTiposLogradouro = [];
                vm.listaUFs = [];
                vm.listaMunicipios = [];

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                vm.ripSelecionado = null;
                vm.BRASIL = 'Brasil';
                vm.cepInformado = false;
                vm.cepNaoGenerico = false;

                var init = function () {

                    vm.tela = avaliacaoEscopoCompartilhadoService.getObjeto('tela');
                    avaliacaoEscopoCompartilhadoService.limparEscopo();
                    pesquisarPaises();
                    pesquisarTiposLogradouro();
                    pesquisarUfsPorJurisdicao();

                };

                vm.buscarImoveisLaudoRelatorio = function () {
                    consultarImovelLaudoRelatorioService.consultarDadosImovelLaudoRelatorio(vm.filtroConsulta, (vm.registroInicial - 1), vm.tamanhoLimite)
                        .then(function (resp) {

                            vm.listaDadosImoveisLaudoRelatorio = resp.data.content;
                            vm.totalItems = resp.data.totalElements;
                            if (vm.listaDadosImoveisLaudoRelatorio.length == 0) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-nenhum-registro-encontrado'));
                            }
                            vm.totalItems = resp.data.totalElements;

                            var ids = [];
                            for (var i = 0; i < resp.data.content.length; i++) {
                                ids.push(resp.data.content[i].id)
                            }
                            consultarAvaliacaoService.pesquisaBenfeitoria(ids).then(function (retorno) {
                                for (var i = 0; i < retorno.data.length; i++) {
                                    var benfeitoria = retorno.data[i];
                                    for (var k = 0; k < vm.listaDadosImoveisLaudoRelatorio.length; k++) {
                                        if (benfeitoria.id === vm.listaDadosImoveisLaudoRelatorio[k].id) {
                                            vm.listaDadosImoveisLaudoRelatorio[k].benfeitoria = benfeitoria;
                                            break
                                        }
                                    }
                                }
                            });
                        });

                };

                vm.voltarCadastrarLaudoRelatorio = function () {
                    var rota = 'avaliacao.' + vm.tela;
                    $state.go(rota);
                };

                vm.limparCamposPesquisa = function () {
                    vm.cepInformado = false;
                    vm.filtroConsulta = {};
                };

                var pesquisarPaises = function () {
                    dominioAvaliacaoService.consultarImovelPaisesSU().then(function (resposta) {
                        vm.listaPaises = resposta.data;

                        angular.forEach(vm.listaPaises, function (pais) {
                            if (pais.descricao === 'Brasil') {
                                vm.idBrasil = pais.id;
                            }
                        });
                    })
                };

                var pesquisarUfsPorJurisdicao = function () {
                    dominioAvaliacaoService.pesquisarUfsIntegrador().then(function (respostaUf) {
                        var listaUFs = respostaUf.data;
                        usuarioAvaliacaoService.getJurisdicao().then(function (respostaJurisdicao) {
                            var listaJurisdicoes = respostaJurisdicao.data;

                            angular.forEach(listaUFs, function (uf) {
                                angular.forEach(listaJurisdicoes, function (jurisdicao) {
                                    if (uf.sigla == jurisdicao) {
                                        vm.listaUFs.push(uf);
                                    }
                                });
                            });
                        });
                    });
                };

                var pesquisarTiposLogradouro = function () {
                    dominioAvaliacaoService.consultarTiposLogradouroSU().then(function (resp) {
                        vm.listaTiposLogradouro = resp.data;
                    });
                };

                vm.pesquisarMunicipiosPorUf = function () {
                    vm.listaMunicipios = [];
                    dominioAvaliacaoService.pesquisarMuniciposPorUfIntegrador(vm.filtroConsulta.uf).then(function (resposta) {
                        vm.listaMunicipios = resposta.data;
                    });
                };

                vm.consultarLocalidadeEnderecoCep = function () {
                    if (vm.filtroConsulta.cep && vm.filtroConsulta.cep != '') {
                        dominioAvaliacaoService.pesquisarLocalidadePorCepIntegrador(vm.filtroConsulta.cep)
                            .then(function (resp) {
                                vm.filtroConsulta.uf = resp.data.uf;
                                vm.filtroConsulta.municipio = resp.data.municipio;
                                vm.filtroConsulta.logradouro = resp.data.logradouro;
                                vm.filtroConsulta.bairro = resp.data.bairro;
                                if (vm.listaUFs.indexOf(vm.filtroConsulta.uf.sigla) !== -1) {
                                    vm.filtroConsulta = {};
                                    vm.cepInformado = false;
                                } else {
                                    vm.cepInformado = true;
                                }
                            }, function () {
                                vm.filtroConsulta.uf = undefined;
                                vm.filtroConsulta.municipio = undefined;
                                vm.filtroConsulta.logradouro = undefined;
                                vm.filtroConsulta.bairro = undefined;

                                vm.cepInformado = false;
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-cep-invalido'));
                            });
                        if (vm.filtroConsulta.cep.endsWith('000')) {
                            vm.cepNaoGenerico = true;
                        }
                    } else {
                        vm.filtroConsulta.uf = undefined;
                        vm.filtroConsulta.municipio = undefined;
                        vm.filtroConsulta.logradouro = undefined;
                        vm.filtroConsulta.bairro = undefined;

                        vm.cepInformado = false;
                    }
                };

                vm.confimarImovelSelecionado = function () {
                    if (vm.ripSelecionado) {
                        $state.go('avaliacao.' + vm.tela, {
                            rip: vm.ripSelecionado
                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-selecione-imovel'));
                    }
                };

                vm.abrirDialogImagensImovel = function (ev, ripImovel) {
                    imovelAvaliacaoService.buscarFotosImovel(ripImovel)
                        .then(function (resp) {
                            var listaFotosImovel = resp.data;

                            if (listaFotosImovel && listaFotosImovel.length > 0) {
                                $mdDialog.show({
                                    controller: ["vm", function (vm) {
                                        vm.ripImovel = ripImovel;
                                        vm.listaFotosImovel = listaFotosImovel;

                                        vm.fechar = function () {
                                            $mdDialog.cancel();
                                        };
                                    }],
                                    templateUrl: 'scripts-avaliacao/app/directives/partial/dialogFotosDetalharImovel.tmpl.html',
                                    parent: angular.element(document.body),
                                    targetEvent: ev,
                                    clickOutsideToClose: true
                                });
                            }
                        }, function (erro) {
                            ComumFactory.mostrarMensagemError(erro.data.message);
                        });
                };

                vm.abrirDialogMapaDetalharImovel = function (ev, pontoImovel, dadosImovelAvaliacao) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    if (dadosImovelAvaliacao) {
                        imovelAvaliacaoService.buscarBenfeitoriasSuPorRip(dadosImovelAvaliacao.rip).then(function (resp) {
                            var benfeitorias = resp.data;
                            $mdDialog.show({
                                controller: ["$scope", function ($scope) {
                                    var vm = $scope;
                                    vm.pontoImovel = pontoImovel;
                                    vm.dadosImovelAvaliacao = dadosImovelAvaliacao;
                                    vm.benfeitorias = benfeitorias;
                                    vm.geometriaTerreno = dadosImovelAvaliacao.benfeitoria ? dadosImovelAvaliacao.benfeitoria.terreno : null;

                                    vm.fecharDialogMapaDetalharImovel = function () {
                                        $mdDialog.cancel();
                                    };
                                }],
                                templateUrl: 'scripts-avaliacao/app/directives/partial/dialogMapaDetalharImovel.tmpl.html',
                                parent: angular.element(document.body),
                                targetEvent: ev,
                                clickOutsideToClose: true,
                                fullscreen: useFullScreen
                            });
                        });
                    }
                }

                    init();
                }]
            )

})();
/**
 * Created by thiago-hirae on 16/05/17.
 */
(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory("consultarAvaliacaoService", ["$http", function($http){
            var URL_DADOS_IMOVEL_AVALIACAO = '/avaliacao/api/dados/imovel/avaliacao';
            var URL_INCORPORACAO = '/avaliacao/api/incorporacao';

            var pesquisaBenfeitoria = function(ids) {
                return $http.post(URL_INCORPORACAO + '/benfeitorias',ids);
            };

            var consultarDadosDetalharImovelAvaliacao = function(filtroConsulta, offset, limit) {
                return $http.get(URL_DADOS_IMOVEL_AVALIACAO + '/consultar/dados', {
                    params: {
                        rip: filtroConsulta.rip,
                        ripSpiunet: filtroConsulta.ripSpiunet,
                        situacaoAvaliacao: filtroConsulta.situacaoAvaliacao,
                        dataInicial: filtroConsulta.dataInicial,
                        dataFinal: filtroConsulta.dataFinal,
                        idTipoImovel: filtroConsulta.idTipoImovel,
                        idPais: filtroConsulta.idPais,
                        uf: filtroConsulta.uf,
                        cep: filtroConsulta.cep,
                        municipio: filtroConsulta.municipio,
                        campoOrdenacao: filtroConsulta.campoOrdenacao,
                        ordem: filtroConsulta.ordem,
                        offset: offset,
                        limit: limit
                    }
                });
            };

            return {
                consultarDadosDetalharImovelAvaliacao: consultarDadosDetalharImovelAvaliacao,
                pesquisaBenfeitoria:pesquisaBenfeitoria
            }
        }])
})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('consultarDadosAvaliacaoController',
            ["$scope", "dominioAvaliacaoService", "$filter", "consultarAvaliacaoService", "formularioAvaliacaoService", "imovelAvaliacaoService", "usuarioAvaliacaoService", "$state", "$mdMedia", "$mdDialog", function ($scope, dominioAvaliacaoService, $filter, consultarAvaliacaoService, formularioAvaliacaoService, imovelAvaliacaoService, usuarioAvaliacaoService,
                      $state, $mdMedia, $mdDialog) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.topDirections = ['left'];
                vm.bottomDirections = ['down', 'right'];
                vm.isOpen = false;
                vm.availableModes = ['md-fling', 'md-scale'];
                vm.selectedMode = 'md-scale';
                vm.availableDirections = ['right'];
                vm.selectedDirection = 'right';
                vm.selectedEstado = true;
                vm.requiredPais = true;
                vm.requiredEstado = true;

                vm.filtroConsulta = {};
                vm.listaTiposImovel = [];
                vm.listaPaises = [];
                vm.listaUFs = [];
                vm.listaMunicipios = [];
                vm.idBrasil = undefined;

                // Não exite serviço para consulta de cidade do exterior. Trecho comentado até definição de regra de negócio.
                //vm.listaCidadesExterior = [];

                vm.listaDadosImoveisAvaliacao = [];
                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                vm.avaliacoesAusentes =
                    {
                        AVALIACAO_AUSENTE_SEM_DADOS: $filter('translate')('select-avaliacoes-ausente-sem-dados'),
                        AVALIACAO_AUSENTE_SEM_LAUDO: $filter('translate')('select-avaliacoes-ausente-sem-laudo'),
                        AVALIACAO_AUSENTE_SEM_RELATORIO: $filter('translate')('select-avaliacoes-ausente-sem-relatorio'),
                    };

                /* Uma vez que os campos 'País' e 'UF' passaram a ser obrigatórios, a ordenação de campos perdeu a finalidade.
                vm.ordenacaoCampos = {
                    UF: 'UF',
                    MUNICIPIO: 'Município'
                };

                vm.CRESCENTE = 'CRESCENTE';
                vm.DECRESCENTE = 'DECRESCENTE';
                */

                vm.limparCamposPesquisa = function () {
                    vm.selectedEstado = true;
                    vm.requiredPais = true;
                    vm.requiredEstado = true;
                    vm.filtroConsulta = {};
                    vm.listaDadosImoveisAvaliacao = [];
                    vm.listaUFs = [];
                    vm.listaMunicipios = [];
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;
                };

                var init = function () {
                    buscarUsuarioLogado(function () {
                        pesquisaTiposImovel();
                        pesquisarPaises();
                    });
                };

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_CONSULTAR_VISUALIZAR_DADOS_AVALIACAO === 'false' && vm.usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });

                };

                var pesquisaTiposImovel = function () {
                    dominioAvaliacaoService.consultarTiposImovelSU().then(function (resposta) {
                        vm.listaTiposImovel = resposta.data;
                    });
                };

                var pesquisarPaises = function () {
                    dominioAvaliacaoService.consultarImovelPaisesSU().then(function (resposta) {
                        vm.listaPaises = resposta.data;
                        angular.forEach(vm.listaPaises, function (pais) {
                            if (pais.descricao === 'Brasil') {
                                vm.idBrasil = pais.id;
                            }
                        });
                    })
                };

                var pesquisarUfs = function () {
                    dominioAvaliacaoService.pesquisarUfsIntegrador().then(function (respostaUf) {
                        vm.listaUFs = respostaUf.data;
                    })
                };

                vm.pesquisarMunicipiosPorUf = function () {
                    if (vm.filtroConsulta.uf) {
                        dominioAvaliacaoService.pesquisarMuniciposPorUfIntegrador(vm.filtroConsulta.uf).then(function (resposta) {
                            vm.listaMunicipios = resposta.data;
                        });
                    }
                };

                vm.verificarObrigatoriedadePais = function () {
                    vm.requiredPais = vm.requiredEstado = !vm.filtroConsulta.rip && !vm.filtroConsulta.ripSpiunet && !vm.filtroConsulta.cep;
                };

                vm.verificarObrigatoriedadeEstado = function () {
                    if (!vm.filtroConsulta.idPais || vm.filtroConsulta.idPais === vm.idBrasil) {
                        vm.requiredEstado = true;
                        vm.selectedEstado = true;
                        pesquisarUfs();
                    } else {
                        vm.selectedEstado = false;
                        vm.requiredEstado = false;
                        vm.filtroConsulta.uf = null;
                        filtroConsulta.municipio = null;
                    }
                };

                vm.formatarEnderecoCompleto = function(endereco) {
                    if (!endereco) return '';

                    var partes = [
                        endereco.logradouro,
                        endereco.numero ? 'Número ' + endereco.numero : null,
                        endereco.bairro,
                        endereco.municipio && endereco.uf ? endereco.municipio + '/' + endereco.uf : null,
                        endereco.pais,
                        endereco.cep ? 'CEP: ' + endereco.cep : null
                    ];

                    return partes.filter(Boolean).join(', ') + '.';
                };

                vm.pesquisarDadosDetalharImovelAvaliacao = function () {
                    vm.validarFormulario(vm.formularioConsultaImovel);

                    if (vm.formularioConsultaImovel.$valid) {
                        vm.filtroConsulta.dataInicial = vm.filtroConsulta.dataInicial
                            ? $filter('date')(vm.filtroConsulta.dataInicial, 'dd-MM-yyyy')
                            : null;

                        vm.filtroConsulta.dataFinal = vm.filtroConsulta.dataFinal
                            ? $filter('date')(vm.filtroConsulta.dataFinal, 'dd-MM-yyyy')
                            : null;

                        vm.filtroConsulta.rip = vm.filtroConsulta.rip
                            ? ("00000000" + vm.filtroConsulta.rip).slice(-8)
                            : null;

                        if (!vm.idSessaoBusca || vm.registroInicial === 1) {
                            vm.idSessaoBusca = crypto.randomUUID();
                        }

                        consultarAvaliacaoService.consultarDadosDetalharImovelAvaliacao(vm.filtroConsulta, (vm.registroInicial - 1), vm.tamanhoLimite)
                            .then(function (resp) {
                                vm.listaDadosImoveisAvaliacao = resp.data.content;
                                vm.totalItems = resp.data.totalElements;

                                var ids = resp.data.content.map(function(item) {
                                    return item.id;
                                });

                                if (ids.length) {
                                    consultarAvaliacaoService.pesquisaBenfeitoria(ids).then(function (retorno) {
                                        retorno.data.forEach(function (benfeitoria) {
                                            for (var k = 0; k < vm.listaDadosImoveisAvaliacao.length; k++) {
                                                if (benfeitoria.id === vm.listaDadosImoveisAvaliacao[k].id) {
                                                    vm.listaDadosImoveisAvaliacao[k].benfeitoria = benfeitoria;
                                                    break;
                                                }
                                            }
                                        });
                                    });
                                }
                            });
                    }
                };

                vm.redirecionarDetalharImovelAvaliacoes = function (rip) {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: rip
                    });
                };

                vm.abrirDialogMapaDetalharImovel = function (ev, pontoImovel, dadosImovelAvaliacao) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    if (dadosImovelAvaliacao) {
                        imovelAvaliacaoService.buscarBenfeitoriasSuPorRip(dadosImovelAvaliacao.rip).then(function (resp) {
                            var benfeitorias = resp.data;
                            $mdDialog.show({
                                controller: ["$scope", function ($scope) {
                                    var vm = $scope;
                                    vm.pontoImovel = pontoImovel;
                                    vm.dadosImovelAvaliacao = dadosImovelAvaliacao;
                                    vm.benfeitorias = benfeitorias;
                                    vm.geometriaTerreno = dadosImovelAvaliacao.benfeitoria ? dadosImovelAvaliacao.benfeitoria.terreno : null;

                                    vm.fecharDialogMapaDetalharImovel = function () {
                                        $mdDialog.cancel();
                                    };
                                }],
                                templateUrl: 'scripts-avaliacao/app/directives/partial/dialogMapaDetalharImovel.tmpl.html',
                                parent: angular.element(document.body),
                                targetEvent: ev,
                                clickOutsideToClose: true,
                                fullscreen: useFullScreen
                            });
                        });
                    }

                };

                if (vm.imovel) {
                    imovelAvaliacaoService.buscarBenfeitoriasSuPorRip(vm.rip).then(function (resp) {
                        vm.imovel.benfeitorias = resp.data;
                    });

                }

                vm.abrirDialogMapaDetalharLocalizacoesImoveis = function (ev, listaDadosImoveisAvaliacao) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", function ($scope) {
                            var vm = $scope;
                            vm.listaDadosImoveisAvaliacao = listaDadosImoveisAvaliacao;

                            vm.fecharDialogMapaDetalharLocalizacoesImoveis = function () {
                                $mdDialog.cancel();
                            };
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogMapaDetalharLocalizacoesImoveis.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                vm.detalheAvaliacao = function (ev, dadosImovelAvaliacao) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", function ($scope) {
                            var vm = $scope;

                            vm.dadosImovelAvaliacao = dadosImovelAvaliacao;
                            var init = function () {

                            };

                            vm.fecharDialogDetalhesDadosAvaliacao = function () {
                                $mdDialog.cancel();
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesDadosAvaliacao.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                init();
            }]);
}());
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cadastrarDocumentoEdificacaoController',
            ["$scope", "avaliacaoEscopoCompartilhadoService", "arquivoAvaliacaoService", "$state", "ComumFactory", "$filter", function ($scope, avaliacaoEscopoCompartilhadoService, arquivoAvaliacaoService, $state, ComumFactory, $filter) {

                var vm = $scope

                vm.fileLimit = 1;

                var content_type = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel',
                    'application/excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.oasis.opendocument.text ', 'application/vnd.oasis.opendocument.spreadsheet'];

                vm.condicoes = [
                    {
                        nome: "Sim",
                        valor: "SIM"

                    }, {
                        nome: "Não",
                        valor: "NAO"
                    }
                ];

                var init = function () {

                    vm.editar = avaliacaoEscopoCompartilhadoService.getObjeto('editar');

                    if (vm.editar.tipo === 'BENFEITORIA') {
                        vm.listaDocumentosEdificacao = avaliacaoEscopoCompartilhadoService.getObjeto('listaDocumentosEdificacaoBenfeitoria');
                    } else if (vm.editar.tipo === 'UNIDADE_AUTONOMA') {
                        vm.listaDocumentosEdificacao = avaliacaoEscopoCompartilhadoService.getObjeto('listaDocumentosEdificacaoUnidadeAutonoma');
                    }

                    recuperarPropriedadesLaudoRelatorio();

                    if (vm.editar.buttonShow === true) {
                        vm.documento = angular.copy(vm.listaDocumentosEdificacao[vm.editar.index]);
                        if (vm.documento.dtEmissao != null) {
                            vm.documento.dtEmissao = new Date(vm.documento.dtEmissao);
                        }
                        if (vm.documento.dtValidade != null) {
                            vm.documento.dtValidade = new Date(vm.documento.dtValidade);
                        }
                    }

                };

                var recuperarPropriedadesLaudoRelatorio = function () {
                    vm.laudoRelatorioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoRelatorioAvaliacao');
                    vm.dadosEnderecamento = avaliacaoEscopoCompartilhadoService.getObjeto('dadosEnderecamento');
                    vm.dadosProprietario = avaliacaoEscopoCompartilhadoService.getObjeto('dadosProprietario');
                    vm.benfeitoria = avaliacaoEscopoCompartilhadoService.getObjeto('benfeitoria');
                    vm.tipoTerreno = avaliacaoEscopoCompartilhadoService.getObjeto('tipoTerreno');

                    /**Identificacao**/
                    vm.ripImovel = avaliacaoEscopoCompartilhadoService.getObjeto('ripImovel');
                    vm.pontoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('pontoImovel');
                    vm.listaResponsaveisTecnicos = avaliacaoEscopoCompartilhadoService.getObjeto('listaResponsaveisTecnicos');
                    vm.listaNormasUtilizadas = avaliacaoEscopoCompartilhadoService.getObjeto('listaNormasUtilizadas');
                    /**Detalher Imovel**/
                    vm.listaMatriculasTranscricao = avaliacaoEscopoCompartilhadoService.getObjeto('listaMatriculasTranscricao');
                    vm.listaCertidoesCartoriais = avaliacaoEscopoCompartilhadoService.getObjeto('listaCertidoesCartoriais');
                    angular.forEach(vm.listaCertidoesCartoriais, function (certidaoCartorial) {
                        certidaoCartorial.arquivo.id = null;
                    });
                    vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                    vm.listaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosImovel');
                    /**Terreno Imovel**/
                    vm.terreno = avaliacaoEscopoCompartilhadoService.getObjeto('terreno');
                    vm.documentosParametrosUrbanisticos = avaliacaoEscopoCompartilhadoService.getObjeto('documentosParametrosUrbanisticos');
                    /**Benfeitorias**/
                    vm.listaBenfeitoriaAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriaAvaliacao');
                    vm.listaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosBenfeitorias');
                    /**Unidade Autonoma**/
                    vm.unidadeAutonomaIncorporacao = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaIncorporacao');
                    vm.listaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosUnidadeAutonoma');
                    /**Vistoria**/
                    vm.vistoriaListaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosImovel');
                    vm.vistoriaListaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosBenfeitorias');
                    vm.vistoriaListaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosUnidadeAutonoma');
                    /**Avaliacao**/
                    vm.tabSelect = avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect');
                    vm.avaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao');

                    vm.formularioIdentificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioIdentificacao');
                    vm.formularioDetalhesImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioDetalhesImovel');
                    vm.formularioTerrenoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioTerrenoImovel');
                    vm.formularioBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('formularioBenfeitorias');
                    vm.formularioUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUnidadeAutonoma');
                    vm.formularioUtilizacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUtilizacao');
                    vm.formularioVistoria = avaliacaoEscopoCompartilhadoService.getObjeto('formularioVistoria');
                    vm.formularioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAvaliacao');
                    vm.formularioResultadosRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosRelatorio');
                    vm.formularioResultadosLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosLaudo');
                    vm.formularioQuantificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacao');
                    vm.formularioQuantificacaoOrcamento = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacaoOrcamento');
                    vm.formularioResultadoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadoAvaliacao');
                    vm.formularioAnexo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAnexo');

                    avaliacaoEscopoCompartilhadoService.limparEscopo();
                };

                var voltarCadastrarLaudoRelatorio = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', vm.tipoTerreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', vm.benfeitoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

                    if (vm.editar.tipo === 'BENFEITORIA') {
                        avaliacaoEscopoCompartilhadoService.setObjetos('listaDocumentosEdificacaoBenfeitoria', vm.listaDocumentosEdificacao);
                    }
                    else if (vm.editar.tipo === 'UNIDADE_AUTONOMA') {
                        avaliacaoEscopoCompartilhadoService.setObjetos('listaDocumentosEdificacaoUnidadeAutonoma', vm.listaDocumentosEdificacao);
                    }
                    vm.editar.tipo = null;
                    avaliacaoEscopoCompartilhadoService.setObjetos('editar', vm.editar);

                    $state.go('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: vm.laudoRelatorioAvaliacao.id
                    })

                };

                vm.fecharInserirNovoDocumento = function () {
                    voltarCadastrarLaudoRelatorio();
                };

                vm.adicionarArquivoDocumento = function (file) {
                    if (content_type.indexOf(file[0].type) != -1) {
                        arquivoAvaliacaoService.uploadArquivo(file[0]).then(function (resp) {
                            vm.documento.arquivo = resp.data;
                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-documento-formato-invalido'));
                    }
                };

                vm.adicionarListaDocumentos = function () {
                    vm.formEnviado = true;

                    if (vm.documento.dtEmissao > new Date() || vm.documento.dtEmissao >= vm.documento.dtValidade) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-periodo-documento-invalido'));
                        return;
                    }

                    if (!vm.documento || !vm.documento.nome || !vm.documento.arquivo || !vm.documento.dtEmissao || vm.documento.isPossui === null) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }


                    if (!vm.listaDocumentosEdificacao) {
                        vm.listaDocumentosEdificacao = [];
                    }

                    vm.listaDocumentosEdificacao.push(vm.documento);
                    vm.formEnviado = false;

                    vm.editar.buttonShow = false;
                    vm.editar.index = null;

                    vm.documento = {};

                    voltarCadastrarLaudoRelatorio();

                };

                vm.gravarAlteracaoListaDocumentos = function () {
                    vm.formEnviado = true;

                    if (vm.documento.isPossui != 'SEM_INFORMACAO') {
                        if (vm.documento.dtEmissao > new Date() || vm.documento.dtEmissao >= vm.documento.dtValidade) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-periodo-documento-invalido'));
                            return;
                        }

                        if (!vm.documento || !vm.documento.nome || !vm.documento.arquivo || !vm.documento.dtEmissao || vm.documento.isPossuiDocumento === null) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                            return;
                        }
                    }

                    if (!vm.listaDocumentosEdificacao) {
                        vm.listaDocumentosEdificacao = [];
                    }

                    vm.listaDocumentosEdificacao.splice(vm.editar.index, 1);

                    vm.listaDocumentosEdificacao.push(vm.documento);
                    vm.formEnviado = false;
                    vm.editar.buttonShow = false;
                    vm.editar.index = null;
                    vm.documento = {};

                    voltarCadastrarLaudoRelatorio();
                };


                init();
            }])

})();
(function(){
    'use strict';
    angular.module('su-avaliacao')
        .factory('dadosUtilizacaoService', ["$http", function ($http) {

            var URL_BASE = '/avaliacao/api/dominio';

            var buscarDadosUtilizacao = function(rip) {
                return $http.get(URL_BASE + '/buscar/dados/destinacoes/' + rip);
            };

            return {
                buscarDadosUtilizacao: buscarDadosUtilizacao
            }
        }]);
}());

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('ApresentarDadosUtilizacaoController',
            ["$scope", "dadosUtilizacaoService", "comumAvaliacaoService", "$stateParams", "$state", "ComumFactory", "$filter", function ($scope, dadosUtilizacaoService, comumAvaliacaoService, $stateParams, $state, ComumFactory, $filter) {

                var vm = $scope;

                vm.responsavel = {}
                vm.pessoa = {}

                var init = function () {
                    vm.detalharDados = true;
                    vm.rip = $stateParams.rip;
                    buscarDadosUtilizacao();
                    buscarTipoPossesOcupacoes();
                    buscarTiposRepresentacao();
                };

                vm.redirecionarParaDetalharImovel = function () {
                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                        ripDetalhar: vm.rip
                    });
                };

                var buscarDadosUtilizacao = function () {
                    dadosUtilizacaoService.buscarDadosUtilizacao(vm.rip).then(function (resp) {
                        vm.dadosUtilizacoes = resp.data;
                    });
                };

                var buscarTipoPossesOcupacoes = function () {
                    vm.listaOcupacoes = {
                        INDIVIDUAL: "Individual",
                        COLETIVO: "Coletivo",
                        OCUPANTE_NAO_IDENTIFICADO: "Ocupante não identificado"
                    }
                };

                var buscarTiposRepresentacao = function () {
                    vm.listaTiposRepresentacao = {
                        FORMAL: "Formal",
                        INFORMAL: "Informal (não exige CNPJ)",
                        SEM_REPRESENTACAO: "Sem representação"
                    }
                };


                vm.detalharPessoaFisica = function (cpf) {
                    if (cpf && cpf.length == 11) {
                        comumAvaliacaoService.buscarPessoaFisica(cpf).then(function (resp) {
                            var pessoa = resp.data;
                            vm.pessoa = pessoa;
                            vm.pessoa.endereco = angular.copy(pessoa.endereco);
                            adicionarTelefones(pessoa.telefones);

                            window.sessionStorage.setItem('dadoPessoaFisica', JSON.stringify(vm.pessoa));
                            var rota = $state.href('avaliacao.DADOS_PESSOAS');
                            window.open(rota, '_blank');

                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-responsavel-invalido'));
                        vm.responsavel.cpf = '';
                        vm.pessoa = null;
                    }

                };


                vm.detalharPessoaJuridica = function (cpfCnpj) {
                    if (cpfCnpj && cpfCnpj.length == 14) {
                        comumAvaliacaoService.buscarPessoaFisica(cpfCnpj).then(function (resp) {
                            var responsavel = resp.data.resultado;
                            vm.responsavel = responsavel;
                            vm.responsavel.cpfCnpj = responsavel.cnpj;
                            vm.responsavel.nome = responsavel.nomeEmpresarial;
                            vm.responsavel.endereco = angular.copy(responsavel.endereco);

                            adicionarTelefones(responsavel.telefones);

                            window.sessionStorage.setItem('dadoPessoaJuridica', JSON.stringify(vm.responsavel));
                            var rota = $state.href('avaliacao.DADOS_PESSOAS_JURIDICA');
                            window.open(rota, '_blank');

                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-cnpj-invalido'));
                        vm.responsavel.cpf = '';
                        vm.pessoa = null;
                    }

                };


                function adicionarTelefones(telefones) {
                    vm.pessoa.telefones = [];
                    angular.forEach(telefones, function (tel) {
                        vm.pessoa.telefones.push({
                            tel: 'Telefone ' + vm.contTelefone,
                            ddd: tel.ddd,
                            numero: tel.numero,
                            isPrincipal: false,
                            contador: vm.contTelefone
                        });
                        vm.contTelefone++;
                    });
                }

                init();
            }])

})();
(function(){
    "use strict";
    dadosPessoasJuridicaController.$inject = ["comumAvaliacaoService", "ComumFactory"];
    angular.module("su-avaliacao").controller("dadosPessoasJuridicaController", dadosPessoasJuridicaController);

    function dadosPessoasJuridicaController(comumAvaliacaoService, ComumFactory) {

        var vm = this;

        vm.historicoPessoaJuridica = [];
        var cont = 0;

        function init() {
            vm.destinacaoPorResponsavel = [];
            vm.responsavel = JSON.parse(window.sessionStorage.getItem('dadoPessoaJuridica'));

            window.sessionStorage.removeItem('dadoPessoaJuridica');

            vm.responsavel.dataSituacaoCadastral = (vm.responsavel.dataSituacaoCadastral ? new Date(vm.responsavel.dataSituacaoCadastral) : null);
            vm.responsavel.dataAbertura = (vm.responsavel.dataAbertura ? new Date(vm.responsavel.dataAbertura) : null);
            vm.responsavel.dataOpcaoSimples = (vm.responsavel.dataOpcaoSimples ? new Date(vm.responsavel.dataOpcaoSimples) : null);
            vm.responsavel.dataExclusaoSimples = (vm.responsavel.dataExclusaoSimples ? new Date(vm.responsavel.dataExclusaoSimples) : null);
            vm.responsavel.dataCriacao = (vm.responsavel.dataCriacao ? new Date(vm.responsavel.dataCriacao) : null);

            buscarHistoricoPessoaJuridica();

        }

        var buscarHistoricoPessoaJuridica = function () {
            var campo = null;
            var obj = {};
            var i = 0;

            campo = ['cnpj', 'nomeEmpresarial', 'nomeFantasia', 'situacaoCadastral', 'dataSituacaoCadastral', 'naturezaJuridica', 'dataAbertura', 'cnaePrincipal', 'cpfResponsavel', 'nomeResponsavel', 'cnpjSucedida'];
            obj = {};
            i = 0;
            angular.forEach(campo, function (campo) {
                comumAvaliacaoService.buscarHistoricoPessoaJuridica(campo, vm.responsavel.cpfCnpj).then(function (retorno) {
                    if (retorno.data.erros && retorno.data.erros.length > 0) {
                        ComumFactory.mostrarMensagemError(retorno.data.erros[0]);
                    } else {
                        obj[campo] = retorno.data.resultado;
                        if (i == 9) {
                            incrementarHistorico(obj);
                            obj = {};
                        }
                    }
                    i++;
                });
            });

        };

        function incrementarHistorico(obj) {
            vm.historicoPessoaJuridica[cont] = obj;
            cont++;
        }


        init();
    }
})();

(function () {
    "use strict";
    directive.$inject = ["comumAvaliacaoService", "ComumFactory"];
    angular.module("su-avaliacao").controller("dadosPessoasController", directive);

    function directive(comumAvaliacaoService, ComumFactory) {

        var vm = this;

        vm.historicoPessoaFisica = [];
        var cont = 0;

        function init() {
            vm.pessoa = JSON.parse(window.sessionStorage.getItem('dadoPessoaFisica'));

            window.sessionStorage.removeItem('dadoPessoaFisica');

            vm.pessoa.dataSituacaoCadastral = (vm.pessoa.dataSituacaoCadastral ? new Date(vm.pessoa.dataSituacaoCadastral) : null);
            vm.pessoa.dataAbertura = (vm.pessoa.dataAbertura ? new Date(vm.pessoa.dataAbertura) : null);
            vm.pessoa.dataOpcaoSimples = (vm.pessoa.dataOpcaoSimples ? new Date(vm.pessoa.dataOpcaoSimples) : null);
            vm.pessoa.dataExclusaoSimples = (vm.pessoa.dataExclusaoSimples ? new Date(vm.pessoa.dataExclusaoSimples) : null);
            vm.pessoa.dataCriacao = (vm.pessoa.dataCriacao ? new Date(vm.pessoa.dataCriacao) : null);

            buscarHistoricoPessoaFisica();

        }


        var buscarHistoricoPessoaFisica = function () {
            var campo = null;
            var obj = {};
            var i = 0;

            campo = ['cpf', 'situacaoCadastral', 'nome', 'dataNascimento', 'anoObito', 'nomeMae', 'sexo', 'estrangeiro', 'tituloEleitor', 'endereco'];
            obj = {};
            i = 0;
            angular.forEach(campo, function (campo) {
                comumAvaliacaoService.buscarHistoricoPessoaFisica(campo, vm.pessoa.cpf).then(function (retorno) {
                    if (retorno.data.erros && retorno.data.erros.length > 0) {
                        ComumFactory.mostrarMensagemError(retorno.data.erros[0]);
                    } else {
                        obj[campo] = retorno.data.resultado;
                        if (i == 9) {
                            incrementarHistorico(obj);
                            obj = {};
                        }
                    }
                    i++;
                });
            });

        };

        function incrementarHistorico(obj) {
            vm.historicoPessoaFisica[cont] = obj;
            cont++;
        }

        init()
    }
})();

(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory("cubAvaliacaoService", ["$http", function($http){
            var URL_CUB = '/avaliacao/api/cub';

            var buscarCubSiapa = function(uf, dataAssinatura, tipoCub) {
                return $http.get(URL_CUB + '/buscar', {
                    params: {
                        dataAssinatura: dataAssinatura,
                        uf: uf,
                        idTipoConstrucao: tipoCub.tipo,
                        idPadrao: tipoCub.padrao
                    }
                });
            };

            var buscarMotivosEstimativaValor = function() {
                return $http.get(URL_CUB + '/motivoEstimativa/find/all');
            };

            var buscarEstadosConservacao = function() {
                return $http.get(URL_CUB + '/estadosConservacao');
            };

            return {
                buscarCubSiapa: buscarCubSiapa,
                buscarMotivosEstimativaValor: buscarMotivosEstimativaValor,
                buscarEstadosConservacao: buscarEstadosConservacao
            }
        }])
})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('informarCubLaudoRelatorioController',
            ["$scope", "avaliacaoEscopoCompartilhadoService", "comumAvaliacaoService", "ComumFactory", "cubAvaliacaoService", "$filter", "formularioAvaliacaoService", "$state", "$mdDialog", function ($scope, avaliacaoEscopoCompartilhadoService, comumAvaliacaoService, ComumFactory,
                      cubAvaliacaoService, $filter, formularioAvaliacaoService, $state, $mdDialog) {

                var vm = $scope;

                vm.listaEstadosConservacao = [];
                vm.listaMotivosEstimativa = [];
                vm.listaCubs = [];
                vm.tiposCub = [];
                vm.cubSelecionado = {};
                vm.estimativa = {};
                vm.listaEstimativas = [];
                vm.valoresTemp = {};

                vm.paginacaoEstimativasValores = [];

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                vm.INFORMAR_VALORES_LAUDO = 'INFORMAR_VALORES_LAUDO';
                vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';

                vm.TIPO_BENFEITORIA = 'TIPO_BENFEITORIA';
                vm.TIPO_UNIDADE_AUTONOMA = 'TIPO_UNIDADE_AUTONOMA';

                vm.msgErro = '';

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                var contador = 0;
                vm.buttonEditarEstimativa =  false;

                var init = function () {
                    recuperarPropriedadesLaudoRelatorio();
                    if (!vm.ripImovel && !vm.laudoRelatorioAvaliacao) {
                        window.history.back();
                    }

                    populaTabelaDepreciacao();

                    preencheTiposCub();
                    pesquisarMotivosEstimativaValor();
                    pesquisarEstadosConservacao(function () {
                        recuperarDadosEdicaoInformarValores();
                    });

                };

                var recuperarPropriedadesLaudoRelatorio = function () {
                    vm.laudoRelatorioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoRelatorioAvaliacao');
                    vm.dadosEnderecamento = avaliacaoEscopoCompartilhadoService.getObjeto('dadosEnderecamento');
                    vm.dadosProprietario = avaliacaoEscopoCompartilhadoService.getObjeto('dadosProprietario');
                    vm.benfeitoria = avaliacaoEscopoCompartilhadoService.getObjeto('benfeitoria');
                    vm.tipoTerreno = avaliacaoEscopoCompartilhadoService.getObjeto('tipoTerreno');
                    vm.tipoValorLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('tipoValorLaudo');

                    /**Identificacao**/
                    vm.ripImovel = avaliacaoEscopoCompartilhadoService.getObjeto('ripImovel');
                    vm.pontoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('pontoImovel');
                    vm.listaResponsaveisTecnicos = avaliacaoEscopoCompartilhadoService.getObjeto('listaResponsaveisTecnicos');
                    vm.listaNormasUtilizadas = avaliacaoEscopoCompartilhadoService.getObjeto('listaNormasUtilizadas');
                    /**Detalher Imovel**/
                    vm.listaMatriculasTranscricao = avaliacaoEscopoCompartilhadoService.getObjeto('listaMatriculasTranscricao');
                    vm.listaCertidoesCartoriais = avaliacaoEscopoCompartilhadoService.getObjeto('listaCertidoesCartoriais');
                    angular.forEach(vm.listaCertidoesCartoriais, function (certidaoCartorial) {
                        certidaoCartorial.arquivo.id = null;
                    });
                    vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                    vm.listaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosImovel');
                    /**Terreno Imovel**/
                    vm.terreno = avaliacaoEscopoCompartilhadoService.getObjeto('terreno');
                    vm.documentosParametrosUrbanisticos = avaliacaoEscopoCompartilhadoService.getObjeto('documentosParametrosUrbanisticos');
                    /**Benfeitorias**/
                    vm.listaBenfeitoriaAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriaAvaliacao');
                    vm.listaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosBenfeitorias');
                    /**Unidade Autonoma**/
                    vm.unidadeAutonomaIncorporacao = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaIncorporacao');
                    vm.listaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosUnidadeAutonoma');
                    /**Vistoria**/
                    vm.vistoriaListaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosImovel');
                    vm.vistoriaListaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosBenfeitorias');
                    vm.vistoriaListaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosUnidadeAutonoma');
                    /**Avaliacao**/
                    vm.tabSelect = avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect');
                    vm.avaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao');

                    vm.formularioIdentificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioIdentificacao');
                    vm.formularioDetalhesImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioDetalhesImovel');
                    vm.formularioTerrenoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioTerrenoImovel');
                    vm.formularioBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('formularioBenfeitorias');
                    vm.formularioUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUnidadeAutonoma');
                    vm.formularioUtilizacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUtilizacao');
                    vm.formularioVistoria = avaliacaoEscopoCompartilhadoService.getObjeto('formularioVistoria');
                    vm.formularioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAvaliacao');
                    vm.formularioResultadosRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosRelatorio');
                    vm.formularioResultadosLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosLaudo');
                    vm.formularioQuantificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacao');
                    vm.formularioQuantificacaoOrcamento = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacaoOrcamento');
                    vm.formularioResultadoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadoAvaliacao');
                    vm.formularioAnexo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAnexo');

                    avaliacaoEscopoCompartilhadoService.limparEscopo();
                };

                var recuperarDadosEdicaoInformarValores = function () {
                    if (vm.tipoTerreno == vm.TIPO_BENFEITORIA) {
                        if (vm.benfeitoria) {
                            vm.valoresTemp.vidaUtil = vm.benfeitoria.vidaUtil;
                            vm.valoresTemp.idadeAparente = vm.benfeitoria.idadeAparente;
                            vm.valoresTemp.valorCub = vm.benfeitoria.valorCub;
                            vm.valoresTemp.valorCubDepreciado = vm.benfeitoria.valorCubDepreciado;
                            vm.valoresTemp.valor = vm.benfeitoria.valor;
                            vm.valoresTemp.isFatoresAcrescimo = vm.benfeitoria.isFatoresAcrescimo;
                            vm.listaEstimativas = (vm.benfeitoria.estimativas ? vm.benfeitoria.estimativas : []);

                            if (vm.benfeitoria.estadoConservacao) {
                                for (var i = 0; i < vm.listaEstadosConservacao.length; i++) {
                                    if (vm.listaEstadosConservacao[i].id === vm.benfeitoria.estadoConservacao.id) {
                                        vm.valoresTemp.estadoConservacao = vm.listaEstadosConservacao[i];
                                    }
                                }
                            }
                            for (i = 0; i < vm.tiposCub.length; i++) {
                                if (vm.tiposCub[i].tipoProjetoCub === vm.benfeitoria.tipoProjetoCub && vm.tiposCub[i].padraoAcabamento === vm.benfeitoria.padraoAcabamento) {
                                    vm.valoresTemp.cubSelecionado = vm.tiposCub[i];
                                }
                            }

                        }
                    } else if (vm.tipoTerreno == vm.TIPO_UNIDADE_AUTONOMA) {
                        if (vm.unidadeAutonomaImovel && vm.unidadeAutonomaImovel.padraoAcabamento && vm.unidadeAutonomaImovel.tipoProjetoCub) {
                            vm.valoresTemp.vidaUtil = vm.unidadeAutonomaImovel.vidaUtil;
                            vm.valoresTemp.idadeAparente = vm.unidadeAutonomaImovel.idadeAparente;
                            vm.valoresTemp.valorCub = vm.unidadeAutonomaImovel.valorCub;
                            vm.valoresTemp.valorCubDepreciado = vm.unidadeAutonomaImovel.valorCubDepreciado;
                            vm.valoresTemp.valor = vm.unidadeAutonomaImovel.valor;
                            vm.valoresTemp.isFatoresAcrescimo = vm.unidadeAutonomaImovel.isFatoresAcrescimo;
                            vm.listaEstimativas = (vm.unidadeAutonomaImovel.estimativas ? vm.unidadeAutonomaImovel.estimativas : []);
                            vm.valoresTemp.estadoConservacao = (vm.unidadeAutonomaImovel.estadoConservacao ? vm.unidadeAutonomaImovel.estadoConservacao : null);

                            for (i = 0; i < vm.listaEstadosConservacao.length; i++) {
                                if (vm.listaEstadosConservacao[i].id === vm.benfeitoria.estadoConservacao.id) {
                                    vm.valoresTemp.estadoConservacao = vm.listaEstadosConservacao[i];
                                }
                            }
                        }
                    }

                    vm.paginarEstimativas();
                };

                var pesquisarEstadosConservacao = function (callback) {
                    cubAvaliacaoService.buscarEstadosConservacao().then(function (resp) {
                        vm.listaEstadosConservacao = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                var pesquisarMotivosEstimativaValor = function (callback) {
                    vm.listaMotivosEstimativa = {
                        BDI: "BDI",
                        ELEVADOR: "Elevador",
                        FUNDACOES_DIRETAS: "Fundações diretas",
                        FUNDACOES_ESPECIAIS: "Fundações especiais",
                        INSTALACOES_ESPECIAIS: "Instalações especiais",
                        OUTRO: "Outro"
                    }
                    if (callback) {
                        callback();
                    }
                };

                var preencheTiposCub = function (callback) {
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Baixo)', 1, 1, 'BAIXO', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Normal)', 1, 2, 'NORMAL', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Alto)', 1, 3, 'ALTO', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Baixo)', 4, 1, 'BAIXO', 'PP4_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Normal)', 4, 2, 'NORMAL', 'PP4_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Baixo)', 2, 1, 'BAIXO', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Normal)', 2, 2, 'NORMAL', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Alto)', 2, 3, 'ALTO', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Normal)', 3, 2, 'NORMAL', 'R16_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Alto)', 3, 3, 'ALTO', 'R16_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PIS, Padrão Residencial, Projeto de interesse social (Baixo)', 10, 1, 'BAIXO', 'PIS_PROJETO_INTERESSE_SOCIAL'));
                    vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Normal)', 5, 2, 'NORMAL', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
                    vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Alto)', 5, 3, 'ALTO', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
                    vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Normal)', 6, 2, 'NORMAL', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Alto)', 6, 3, 'ALTO', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Normal)', 7, 2, 'NORMAL', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Alto)', 7, 3, 'ALTO', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('GI, Padrão Galpão Indústrial, 1 pavimento', 8, 1, 'BAIXO', 'GI_PADRAO_GALPAO_INDUSTRIAL'));
                    vm.tiposCub.push(new TipoCub('RP1Q, Padrão Residencia Popular, 1 pavimento', 9, 1, 'BAIXO', 'RP1Q_PADRAO_RESIDENCIAL_POPULAR_QUARTO'));
                    vm.tiposCub.push(new TipoCub('CUB Médio', null, null, 'CUB_MEDIO', 'CUB_MEDIO'));

                    function TipoCub(nome, tipo, padrao, padraoAcabamento, tipoProjetoCub) {
                        this.nome = nome;
                        this.tipo = tipo;
                        this.padrao = padrao;
                        this.padraoAcabamento = padraoAcabamento;
                        this.tipoProjetoCub = tipoProjetoCub;
                    }

                    if (callback) {
                        callback();
                    }
                };

                vm.buscarValoresCubSiapa = function (tipoCub) {
                    var date = new Date();

                    cubAvaliacaoService.buscarCubSiapa(vm.imovel.enderecamento.uf, date, tipoCub).then(function (resp) {
                        vm.listaCubs = resp.data.cubs;
                        vm.valoresTemp.valorCub = tipoCub.nome === 'CUB Médio' ? resp.data.cubs[0].valorMedio : resp.data.cubs[0].valor;
                        vm.calcularValorEstimado();

                        if (resp.data.exibirMensagem) {
                            var msg = 'Não existe CUB cadastrado para o mês/ano solicitado. O valor apresentado é referente a ';
                            msg += resp.data.mes;
                            msg += '/';
                            msg += resp.data.ano;
                            msg += '.';
                            vm.msgErro = msg;
                            ComumFactory.mostrarMensagemSucesso(msg);
                        } else {
                            vm.msgErro = '';
                        }
                    }, function () {
                        var confirm = $mdDialog.confirm({focusOnOpen: false})
                            .title($filter('translate')('title-nenhum-valor-encontrado'))
                            .textContent($filter('translate')('mensagem-nenhum-valor-encontrado'))
                            .ok($filter('translate')('btn-sim'))
                            .cancel($filter('translate')('btn-nao'));
                        $mdDialog.show(confirm).then(function () {
                            comumAvaliacaoService.buscarUrlsMenusPorAmbiente().then(function (resp) {
                                var urlSiapa = resp;
                                window.open('#/integrador/siapa?url=' + urlSiapa.data.resposta[0].siapa + '/pages/avaliacao/mantercub/consultar.xhtml');
                            })
                        }, function () {
                            $mdDialog.cancel();
                        });

                    });
                };

                vm.selecionarCubSiapa = function () {
                    if (vm.cubSelecionado.valorMedio) {
                        vm.valoresTemp.valorCub = angular.copy(vm.cubSelecionado.valorMedio);
                        vm.calcularValorEstimado();
                    }
                };

                vm.adicionarEstimativaValorCub = function () {
                    if (!vm.estimativa || !vm.estimativa.motivo || !vm.estimativa.porcentagem || !vm.estimativa.justificativaFonte || (vm.estimativa.motivo === 'OUTRO' && !vm.estimativa.especificacao)) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }
                    if (!vm.listaEstimativas) {
                        vm.listaEstimativas = [];
                    }

                    vm.estimativa.cont = angular.copy(vm.estimativa.cont ? vm.estimativa.cont : contador);
                    vm.listaEstimativas.push(vm.estimativa);

                    vm.buttonEditarEstimativa =  false;
                    vm.calcularValorEstimado();
                    vm.paginarEstimativas();
                    vm.estimativa = {};
                    contador++;
                };

                vm.editarEstimativaValor = function (estimativaValor) {
                    vm.estimativa = estimativaValor;
                    vm.buttonEditarEstimativa =  true;
                    reordenarListaEstimativa(estimativaValor.cont);
                };

                vm.removerEstimativaValor = function (cont) {
                    var confirm = $mdDialog.confirm({focusOnOpen: false})
                        .title($filter('translate')('label-titulo-confirmacao'))
                        .textContent($filter('translate')('mensagem-confirmar-deseja-remover-fator'))
                        .ok($filter('translate')('btn-sim'))
                        .cancel($filter('translate')('btn-nao'));
                    $mdDialog.show(confirm).then(function () {
                        reordenarListaEstimativa(cont);
                    }, function () {
                        $mdDialog.cancel();
                    });
                };

                var reordenarListaEstimativa = function (cont) {
                    var listaTemp = [];
                    angular.forEach(vm.listaEstimativas, function (estimativaCub) {
                        if (estimativaCub.cont !== cont) {
                            listaTemp.push(estimativaCub);
                        }
                    });
                    vm.listaEstimativas = listaTemp;
                    vm.calcularValorEstimado();
                    vm.paginarEstimativas();
                };

                var calcularValoresEstimativasCub = function () {
                    var somaPorcentagens = 0;
                    angular.forEach(vm.listaEstimativas, function (estimativaValor) {
                        somaPorcentagens += estimativaValor.porcentagem;
                    });
                    vm.valoresTemp.valorCub = (vm.cubSelecionado.valorMedio / 100 * somaPorcentagens) + vm.cubSelecionado.valorMedio;
                    vm.calcularValorEstimado();

                };

                vm.calcularValorEstimado = function () {
                    if (verificaIdadeAparente() && vm.valoresTemp.cubSelecionado && vm.valoresTemp.estadoConservacao && vm.valoresTemp.valorCub) {
                        if (vm.valoresTemp.idadeAparente !== 0) {
                            var porcentagemVida = (vm.valoresTemp.idadeAparente * 100) / vm.valoresTemp.vidaUtil;
                            porcentagemVida = Math.round(porcentagemVida * 100) / 100;

                            porcentagemVida = arredondaNumero(porcentagemVida);

                            var valorSubtracao = vm.valoresTemp.estadoConservacao.id === 9 ? 2 : 1;
                            var valorTabela = vm.tabelaDepreciacao['coluna' + porcentagemVida][vm.valoresTemp.estadoConservacao.id - valorSubtracao];

                            var valorDepreciacao = 100 - valorTabela;

                            vm.valoresTemp.valorCubDepreciado = (vm.valoresTemp.valorCub * valorDepreciacao) / 100;

                            var somaMotivos = 0;

                            for (var i = 0; i < vm.listaEstimativas.length; i++) {
                                somaMotivos += vm.listaEstimativas[i].porcentagem;
                            }

                            vm.valoresTemp.valor = ((1 + (somaMotivos / 100)) * vm.valoresTemp.valorCubDepreciado) * vm.benfeitoria.areaConstruida;

                        } else {
                            vm.valoresTemp.valorCubDepreciado = 0;
                        }
                    }

                };

                function arredondaNumero(porcentagemVida) {
                    var valor = 2 * Math.round(porcentagemVida / 2);

                    if (valor < 2) {
                        return 2;
                    } else if (valor > 100) {
                        return 100;
                    } else if (valor % 2 !== 0) {
                        return valor + 1;
                    } else {
                        return valor;
                    }

                }

                function verificaIdadeAparente() {
                    if (vm.valoresTemp.idadeAparente > vm.valoresTemp.vidaUtil) {
                        vm.valoresTemp.vidaUtil = '';
                        vm.valoresTemp.idadeAparente = '';
                        ComumFactory.mostrarMensagemError('A idade aparente do imóvel não pode ser maior que a vida útil.');
                        return false;
                    } else if (vm.valoresTemp.vidaUtil === 0) {
                        vm.valoresTemp.vidaUtil = '';
                        ComumFactory.mostrarMensagemError('A Vida Útil não pode ser igual a 0');
                        return false;
                    }
                    return true;
                }

                vm.paginarEstimativas = function () {
                    vm.totalItems = vm.listaEstimativas.length;

                    vm.paginacaoEstimativasValores = [];
                    vm.totalItems = vm.listaEstimativas.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginacaoEstimativasValores = vm.listaEstimativas;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginacaoEstimativasValores.push(vm.listaEstimativas[i]);
                            if (vm.paginacaoEstimativasValores.length === vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                vm.limparDadosFatoresAcrescimo = function () {
                    vm.listaEstimativas = [];
                    vm.paginarEstimativas();
                };

                vm.fecharEstimativasValor = function () {
                    voltarCadastrarLaudoRelatorio();
                };

                vm.gravarValoresCub = function (formularioDados) {
                    vm.validarFormulario(formularioDados);

                    if (!formularioDados.$valid) {
                        return;
                    }

                    if (vm.tipoTerreno === vm.TIPO_BENFEITORIA) {
                        vm.benfeitoria.vidaUtil = vm.valoresTemp.vidaUtil;
                        vm.benfeitoria.idadeAparente = vm.valoresTemp.idadeAparente;
                        vm.benfeitoria.valorCub = vm.valoresTemp.valorCub;
                        vm.benfeitoria.valorCubDepreciado = vm.valoresTemp.valorCubDepreciado;
                        if(vm.tipoValorLaudo === vm.ESTIMAR_VALORES) {
                            vm.benfeitoria.valor = vm.valoresTemp.valor;
                        }
                        vm.benfeitoria.estimativas = vm.listaEstimativas;
                        vm.benfeitoria.isFatoresAcrescimo = vm.valoresTemp.isFatoresAcrescimo;
                        vm.benfeitoria.estadoConservacao = vm.valoresTemp.estadoConservacao;
                        vm.benfeitoria.padraoAcabamento = vm.valoresTemp.cubSelecionado.padraoAcabamento;
                        vm.benfeitoria.tipoProjetoCub = vm.valoresTemp.cubSelecionado.tipoProjetoCub;

                        angular.forEach(vm.listaBenfeitoriasImovel, function (benfeitoriaImovel) {
                            if (benfeitoriaImovel.id === vm.benfeitoria.id) {
                                benfeitoriaImovel = angular.copy(vm.benfeitoria);
                            }
                        });
                    } else if (vm.tipoTerreno === vm.TIPO_UNIDADE_AUTONOMA) {
                        vm.unidadeAutonomaImovel.vidaUtil = vm.valoresTemp.vidaUtil;
                        vm.unidadeAutonomaImovel.idadeAparente = vm.valoresTemp.idadeAparente;
                        vm.unidadeAutonomaImovel.valorCub = vm.valoresTemp.valorCub;
                        vm.unidadeAutonomaImovel.valorCubDepreciado = vm.valoresTemp.valorCubDepreciado;
                        vm.unidadeAutonomaImovel.valor = vm.valoresTemp.valor;
                        vm.unidadeAutonomaImovel.estimativas = vm.listaEstimativas;
                        vm.unidadeAutonomaImovel.isFatoresAcrescimo = vm.valoresTemp.isFatoresAcrescimo;
                        vm.unidadeAutonomaImovel.estadoConservacao = vm.valoresTemp.estadoConservacao;
                    }

                    voltarCadastrarLaudoRelatorio();
                };

                vm.showDialogDepreciacaoHeidecke = function (ev) {
                    $mdDialog.show({
                        controller: function () {

                            vm.downloadPlanilha = function(){
                                window.open('assets/docs/Tab_Ross.pdf', "_blank");
                            }
                            
                            return vm;
                        },
                        controllerAs: 'vm',
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDepreciacaoHeidecke.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: true
                    });
                };

                vm.cancelModal = function () {
                    $mdDialog.cancel();
                };

                vm.limparEstimativaValorCub = function () {
                    vm.estimativa = {};
                };

                function populaTabelaDepreciacao() {
                    vm.tabelaDepreciacao = {
                        coluna2: [1.02, 1.05, 3.51, 9.03, 18.9, 33.9, 53.1, 75.4],
                        coluna4: [2.08, 2.11, 4.55, 10, 19.8, 34.6, 53.6, 75.7],
                        coluna6: [3.18, 3.21, 5.62, 11, 20.7, 35.3, 54.1, 76],
                        coluna8: [4.32, 4.35, 6.73, 12.1, 21.6, 36.1, 54.6, 76.3],
                        coluna10: [5.5, 5.53, 7.88, 13.2, 22.6, 36.9, 55.2, 76.6],
                        coluna12: [6.72, 6.75, 9.07, 14.3, 23.6, 37.7, 55.8, 76.9],
                        coluna14: [7.98, 8.01, 10.3, 15.4, 24.6, 38.5, 56.4, 77.2],
                        coluna16: [9.28, 9.31, 11.6, 16.6, 25.7, 39.4, 57, 77.5],
                        coluna18: [10.6, 10.6, 12.9, 17.8, 26.8, 40.3, 57.6, 77.8],
                        coluna20: [12, 12, 14.2, 19.1, 27.9, 42.2, 58.3, 78.2],
                        coluna22: [13.4, 13.4, 15.6, 20.4, 29.1, 42.2, 59, 78.5],
                        coluna24: [14.9, 14.9, 17, 21.8, 30.3, 43.1, 59.6, 78.9],
                        coluna26: [16.4, 16.4, 18.5, 23.1, 31.5, 44.1, 60.4, 79.3],
                        coluna28: [17.9, 17, 20, 24.6, 32.8, 45.2, 61.1, 79.6],
                        coluna30: [19.5, 19.5, 21.5, 26, 34.1, 46.2, 61.8, 80],
                        coluna32: [21.1, 21.1, 23.1, 27.5, 35.4, 47.3, 62.6, 80.4],
                        coluna34: [22.8, 22.8, 24.7, 29, 36.8, 48.4, 63.4, 80.8],
                        coluna36: [24.5, 24.5, 26.4, 30.5, 38.1, 49.5, 64.2, 81.3],
                        coluna38: [26.2, 26.2, 28.1, 32.2, 39.6, 50.7, 65, 81.7],
                        coluna40: [28.8, 28.8, 29.9, 33.8, 41, 51.9, 65.9, 82.1],
                        coluna42: [29.8, 29.8, 31.6, 35.5, 42.5, 53.1, 66.7, 82.6],
                        coluna44: [31.7, 31.7, 33.4, 37.2, 44, 54.4, 67.6, 83.1],
                        coluna46: [33.6, 33.6, 35.2, 38.9, 45.6, 55.6, 68.5, 83.5],
                        coluna48: [35.5, 35.5, 37.1, 40.7, 47.2, 56.9, 69.4, 84],
                        coluna50: [37.5, 37.5, 39.1, 42.6, 48.8, 58.2, 70.4, 84.5],
                        coluna52: [39.5, 39.5, 41.9, 44, 50.5, 59.6, 71.3, 85],
                        coluna54: [41.6, 41.6, 43, 46.3, 52.1, 61, 72.3, 85.5],
                        coluna56: [43.7, 43.7, 45.1, 48.2, 53.9, 62.4, 63.3, 86],
                        coluna58: [45.8, 45.8, 47.2, 50.2, 55.6, 63.8, 74.3, 86.6],
                        coluna60: [48.8, 48.8, 49.3, 52.2, 57.4, 65.3, 75.3, 87.1],
                        coluna62: [50.2, 50.2, 51.5, 54.2, 59.2, 66.7, 75.4, 87.7],
                        coluna64: [52.5, 52.5, 53.7, 56.3, 61.1, 61.3, 77.5, 88.2],
                        coluna66: [54.8, 54.8, 55.9, 58.4, 69, 69.8, 78.6, 88.8],
                        coluna68: [57.1, 57.1, 58.2, 60.6, 64.9, 71.4, 79.7, 89.4],
                        coluna70: [59.5, 59.5, 60.5, 62.8, 66.8, 72.9, 80.8, 90.8],
                        coluna72: [62.2, 61.9, 62.9, 85, 68.8, 74.6, 81.9, 90.6],
                        coluna74: [64.4, 64.4, 65.3, 67.3, 70.8, 76.2, 83.1, 91.2],
                        coluna76: [66.9, 66.9, 67.7, 69.6, 72.9, 77.9, 84.3, 91.8],
                        coluna78: [69.4, 69.4, 72.7, 71.9, 74.9, 89.6, 85.5, 92.4],
                        coluna80: [72, 72, 72.7, 74.3, 77.1, 81.3, 86.7, 93.1],
                        coluna82: [74.6, 74.6, 75.3, 76.7, 79.2, 83, 88, 93.7],
                        coluna84: [77.3, 77.3, 77.8, 79.1, 81.4, 84.8, 89.2, 94.4],
                        coluna86: [80, 80, 80.5, 81.6, 83.6, 86.6, 90.5, 95],
                        coluna88: [82.7, 82.7, 83.2, 84.1, 85.8, 88.5, 91.8, 95.7],
                        coluna90: [85.5, 85.5, 85.9, 86.7, 88.1, 90.3, 93.1, 96.4],
                        coluna92: [88.3, 83.3, 88.6, 89.3, 90.4, 92.7, 94.5, 97.1],
                        coluna94: [91.2, 91.2, 91.4, 91.9, 92.8, 94.1, 95.8, 97.8],
                        coluna96: [94.1, 94.1, 94.2, 94.6, 95.1, 96, 97.2, 98.5],
                        coluna98: [97, 97, 97.1, 97.3, 97.6, 98, 98, 99.8],
                        coluna100: [100, 100, 100, 100, 100, 100, 100, 100]
                    };
                }

                var voltarCadastrarLaudoRelatorio = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', vm.tipoTerreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', vm.benfeitoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

                    $state.go('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: vm.laudoRelatorioAvaliacao.id
                    });
                };

                init();
            }]);
}());
(function () {
    'use strict';

    angular.module('su-avaliacao').controller('informarCubAvaliacaoController', ["$scope", "avaliacaoEscopoCompartilhadoService", "comumAvaliacaoService", "ComumFactory", "cubAvaliacaoService", "$filter", "formularioAvaliacaoService", "$state", "$mdDialog", function ($scope, avaliacaoEscopoCompartilhadoService, comumAvaliacaoService, ComumFactory, cubAvaliacaoService, $filter, formularioAvaliacaoService, $state, $mdDialog) {

        var vm = $scope;

        vm.listaEstadosConservacao = [];
        vm.listaMotivosEstimativa = [];
        vm.listaCubs = [];
        vm.tiposCub = [];
        vm.cubSelecionado = {};
        vm.estimativa = {};
        vm.listaEstimativas = [];
        vm.valoresTemp = {};

        vm.paginacaoEstimativasValores = [];

        vm.registroInicial = 1;
        vm.tamanhoLimite = 5;
        vm.totalItems = 0;

        vm.INFORMAR_VALORES_LAUDO = 'INFORMAR_VALORES_LAUDO';
        vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';

        vm.TIPO_BENFEITORIA = 'TIPO_BENFEITORIA';
        vm.TIPO_UNIDADE_AUTONOMA = 'TIPO_UNIDADE_AUTONOMA';

        vm.msgErro = '';

        var formularioServiceInstance = formularioAvaliacaoService.getInstance();
        vm.validarFormulario = formularioServiceInstance.validarFormulario;

        var contador = 0;
        vm.buttonEditarEstimativa = false;

        var init = function () {

            recuperarPropriedadesLaudoAvaliacao();
            if (!vm.rip && !vm.laudoAvaliacao) {
                window.history.back();
            }

            populaTabelaDepreciacao();

            preencheTiposCub();
            pesquisarMotivosEstimativaValor();
            pesquisarEstadosConservacao(function () {
                recuperarDadosEdicaoInformarValores();
            });

        };

        var recuperarPropriedadesLaudoAvaliacao = function () {
            vm.rip = avaliacaoEscopoCompartilhadoService.getObjeto('rip');
            vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
            vm.laudoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoAvaliacao');
            vm.listaBenfeitoriasImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriasImovel');
            vm.unidadeAutonomaImovel = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaImovel');
            vm.tipoTerreno = avaliacaoEscopoCompartilhadoService.getObjeto('tipoTerreno');
            vm.benfeitoria = avaliacaoEscopoCompartilhadoService.getObjeto('benfeitoria');
            avaliacaoEscopoCompartilhadoService.limparEscopo();
        };

        var recuperarDadosEdicaoInformarValores = function () {
            if (vm.tipoTerreno == vm.TIPO_BENFEITORIA) {
                if (vm.benfeitoria) {
                    vm.valoresTemp.vidaUtil = vm.benfeitoria.vidaUtil;
                    vm.valoresTemp.idadeAparente = vm.benfeitoria.idadeAparente;
                    vm.valoresTemp.valorCub = vm.benfeitoria.valorCub;
                    vm.valoresTemp.valorCubDepreciado = vm.benfeitoria.valorCubDepreciado;
                    vm.valoresTemp.valor = vm.benfeitoria.valor;
                    vm.valoresTemp.isFatoresAcrescimo = vm.benfeitoria.isFatoresAcrescimo;
                    vm.listaEstimativas = (vm.benfeitoria.estimativas ? vm.benfeitoria.estimativas : []);

                    if (vm.benfeitoria.estadoConservacao) {
                        for (var i = 0; i < vm.listaEstadosConservacao.length; i++) {
                            if (vm.listaEstadosConservacao[i].id === vm.benfeitoria.estadoConservacao.id) {
                                vm.valoresTemp.estadoConservacao = vm.listaEstadosConservacao[i];
                            }
                        }
                    }
                    for (i = 0; i < vm.tiposCub.length; i++) {
                        if (vm.tiposCub[i].tipoProjetoCub === vm.benfeitoria.tipoProjetoCub && vm.tiposCub[i].padraoAcabamento === vm.benfeitoria.padraoAcabamento) {
                            vm.valoresTemp.cubSelecionado = vm.tiposCub[i];
                        }
                    }

                }
            } else if (vm.tipoTerreno == vm.TIPO_UNIDADE_AUTONOMA) {
                if (vm.unidadeAutonomaImovel && vm.unidadeAutonomaImovel.length > 0 && vm.unidadeAutonomaImovel[0].padraoAcabamento && vm.unidadeAutonomaImovel[0].tipoProjetoCub) {
                    vm.valoresTemp.vidaUtil = vm.unidadeAutonomaImovel[0].vidaUtil;
                    vm.valoresTemp.idadeAparente = vm.unidadeAutonomaImovel[0].idadeAparente;
                    vm.valoresTemp.valorCub = vm.unidadeAutonomaImovel[0].valorCub;
                    vm.valoresTemp.valorCubDepreciado = vm.unidadeAutonomaImovel[0].valorCubDepreciado;
                    vm.valoresTemp.valor = vm.unidadeAutonomaImovel[0].valor;
                    vm.valoresTemp.isFatoresAcrescimo = vm.unidadeAutonomaImovel[0].isFatoresAcrescimo;
                    vm.listaEstimativas = (vm.unidadeAutonomaImovel[0].estimativas ? vm.unidadeAutonomaImovel[0].estimativas : []);

                    if (vm.unidadeAutonomaImovel[0].estadoConservacao) {
                        for (var i = 0; i < vm.listaEstadosConservacao.length; i++) {
                            if (vm.listaEstadosConservacao[i].id === vm.unidadeAutonomaImovel[0].estadoConservacao.id) {
                                vm.valoresTemp.estadoConservacao = vm.listaEstadosConservacao[i];
                            }
                        }
                    }
                    for (i = 0; i < vm.tiposCub.length; i++) {
                        if (vm.tiposCub[i].tipoProjetoCub === vm.unidadeAutonomaImovel[0].tipoProjetoCub && vm.tiposCub[i].padraoAcabamento === vm.unidadeAutonomaImovel[0].padraoAcabamento) {
                            vm.valoresTemp.cubSelecionado = vm.tiposCub[i];
                        }
                    }
                }
            }

            vm.paginarEstimativas();
        };

        var pesquisarEstadosConservacao = function (callback) {
            cubAvaliacaoService.buscarEstadosConservacao().then(function (resp) {
                vm.listaEstadosConservacao = resp.data;
                if (callback) {
                    callback();
                }
            });
        };

        var pesquisarMotivosEstimativaValor = function (callback) {
            vm.listaMotivosEstimativa = {
                BDI: "BDI",
                ELEVADOR: "Elevador",
                FUNDACOES_DIRETAS: "Fundações diretas",
                FUNDACOES_ESPECIAIS: "Fundações especiais",
                INSTALACOES_ESPECIAIS: "Instalações especiais",
                OUTRO: "Outro"
            }
            if (callback) {
                callback();
            }
        };

        var preencheTiposCub = function (callback) {
            vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Baixo)', 1, 1, 'BAIXO', 'R1_PADRAO_RESIDENCIAL'));
            vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Normal)', 1, 2, 'NORMAL', 'R1_PADRAO_RESIDENCIAL'));
            vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Alto)', 1, 3, 'ALTO', 'R1_PADRAO_RESIDENCIAL'));
            vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Baixo)', 4, 1, 'BAIXO', 'PP4_PADRAO_RESIDENCIAL'));
            vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Normal)', 4, 2, 'NORMAL', 'PP4_PADRAO_RESIDENCIAL'));
            vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Baixo)', 2, 1, 'BAIXO', 'R8_PADRAO_RESIDENCIAL'));
            vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Normal)', 2, 2, 'NORMAL', 'R8_PADRAO_RESIDENCIAL'));
            vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Alto)', 2, 3, 'ALTO', 'R8_PADRAO_RESIDENCIAL'));
            vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Normal)', 3, 2, 'NORMAL', 'R16_PADRAO_RESIDENCIAL'));
            vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Alto)', 3, 3, 'ALTO', 'R16_PADRAO_RESIDENCIAL'));
            vm.tiposCub.push(new TipoCub('PIS, Padrão Residencial, Projeto de interesse social (Baixo)', 10, 1, 'BAIXO', 'PIS_PROJETO_INTERESSE_SOCIAL'));
            vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Normal)', 5, 2, 'NORMAL', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
            vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Alto)', 5, 3, 'ALTO', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
            vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Normal)', 6, 2, 'NORMAL', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
            vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Alto)', 6, 3, 'ALTO', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
            vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Normal)', 7, 2, 'NORMAL', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
            vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Alto)', 7, 3, 'ALTO', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
            vm.tiposCub.push(new TipoCub('GI, Padrão Galpão Indústrial, 1 pavimento', 8, 1, 'BAIXO', 'GI_PADRAO_GALPAO_INDUSTRIAL'));
            vm.tiposCub.push(new TipoCub('RP1Q, Padrão Residencia Popular, 1 pavimento', 9, 1, 'BAIXO', 'RP1Q_PADRAO_RESIDENCIAL_POPULAR_QUARTO'));
            vm.tiposCub.push(new TipoCub('CUB Médio', null, null, 'CUB_MEDIO', 'CUB_MEDIO'));

            function TipoCub(nome, tipo, padrao, padraoAcabamento, tipoProjetoCub) {
                this.nome = nome;
                this.tipo = tipo;
                this.padrao = padrao;
                this.padraoAcabamento = padraoAcabamento;
                this.tipoProjetoCub = tipoProjetoCub;
            }

            if (callback) {
                callback();
            }
        };

        vm.buscarValoresCubSiapa = function (tipoCub) {
            var date = vm.laudoAvaliacao.dataAssinatura ? vm.laudoAvaliacao.dataAssinatura : new Date();

            cubAvaliacaoService.buscarCubSiapa(vm.imovel.enderecamento.uf, date, tipoCub).then(function (resp) {
                vm.listaCubs = resp.data.cubs;
                vm.valoresTemp.valorCub = tipoCub.nome === 'CUB Médio' ? resp.data.cubs[0].valorMedio : resp.data.cubs[0].valor;
                vm.calcularValorEstimado();

                if (resp.data.exibirMensagem) {
                    var msg = 'Não existe CUB cadastrado para o mês/ano solicitado. O valor apresentado é referente a ';
                    msg += resp.data.mes;
                    msg += '/';
                    msg += resp.data.ano;
                    msg += '.';
                    vm.msgErro = msg;
                    ComumFactory.mostrarMensagemSucesso(msg);
                } else {
                    vm.msgErro = '';
                }
            }, function () {
                var confirm = $mdDialog.confirm({focusOnOpen: false})
                    .title($filter('translate')('title-nenhum-valor-encontrado'))
                    .textContent($filter('translate')('mensagem-nenhum-valor-encontrado'))
                    .ok($filter('translate')('btn-sim'))
                    .cancel($filter('translate')('btn-nao'));
                $mdDialog.show(confirm).then(function () {
                    comumAvaliacaoService.buscarUrlsMenusPorAmbiente().then(function (resp) {
                        var urlSiapa = resp;
                        window.open('#/integrador/siapa?url=' + urlSiapa.data.resposta[0].siapa + '/pages/avaliacao/mantercub/consultar.xhtml');
                    })

                }, function () {
                    $mdDialog.cancel();
                });

            });
        };

        vm.selecionarCubSiapa = function () {
            if (vm.cubSelecionado.valorMedio) {
                vm.valoresTemp.valorCub = angular.copy(vm.cubSelecionado.valorMedio);
                vm.calcularValorEstimado();
            }
        };

        vm.adicionarEstimativaValorCub = function () {
            if (!vm.estimativa || !vm.estimativa.motivo || !vm.estimativa.porcentagem || !vm.estimativa.justificativaFonte || (vm.estimativa.motivo === 'OUTRO' && !vm.estimativa.especificacao)) {
                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                return;
            }
            if (!vm.listaEstimativas) {
                vm.listaEstimativas = [];
            }

            vm.estimativa.cont = angular.copy(vm.estimativa.cont ? vm.estimativa.cont : contador);
            vm.listaEstimativas.push(vm.estimativa);

            vm.buttonEditarEstimativa = false;
            vm.calcularValorEstimado();
            vm.paginarEstimativas();
            vm.estimativa = {};
            contador++;
        };

        vm.editarEstimativaValor = function (estimativaValor) {
            vm.estimativa = estimativaValor;
            vm.buttonEditarEstimativa = true;
            reordenarListaEstimativa(estimativaValor.cont);
        };

        vm.removerEstimativaValor = function (cont) {
            var confirm = $mdDialog.confirm({focusOnOpen: false})
                .title($filter('translate')('label-titulo-confirmacao'))
                .textContent($filter('translate')('mensagem-confirmar-deseja-remover-fator'))
                .ok($filter('translate')('btn-sim'))
                .cancel($filter('translate')('btn-nao'));
            $mdDialog.show(confirm).then(function () {
                reordenarListaEstimativa(cont);
            }, function () {
                $mdDialog.cancel();
            });
        };

        var reordenarListaEstimativa = function (cont) {
            var listaTemp = [];
            angular.forEach(vm.listaEstimativas, function (estimativaCub) {
                if (estimativaCub.cont != cont) {
                    listaTemp.push(estimativaCub);
                }
            });
            vm.listaEstimativas = listaTemp;
            vm.calcularValorEstimado();
            vm.paginarEstimativas();
        };

        var calcularValoresEstimativasCub = function () {
            if (vm.laudoAvaliacao.tipoValorLaudo === vm.ESTIMAR_VALORES) {
                var somaPorcentagens = 0;
                angular.forEach(vm.listaEstimativas, function (estimativaValor) {
                    somaPorcentagens += estimativaValor.porcentagem;
                });
                vm.valoresTemp.valorCub = (vm.cubSelecionado.valorMedio / 100 * somaPorcentagens) + vm.cubSelecionado.valorMedio;
                vm.calcularValorEstimado();
            }
        };

        vm.calcularValorEstimado = function () {
            if (vm.laudoAvaliacao.tipoValorLaudo === vm.ESTIMAR_VALORES) {
                if (verificaIdadeAparente() && vm.valoresTemp.cubSelecionado && vm.valoresTemp.estadoConservacao && vm.valoresTemp.valorCub) {
                    if (vm.valoresTemp.idadeAparente !== 0) {
                        var porcentagemVida = (vm.valoresTemp.idadeAparente * 100) / vm.valoresTemp.vidaUtil;
                        porcentagemVida = Math.round(porcentagemVida * 100) / 100;

                        porcentagemVida = arredondaNumero(porcentagemVida);

                        var valorSubtracao = vm.valoresTemp.estadoConservacao.id === 9 ? 2 : 1;
                        var valorTabela = vm.tabelaDepreciacao['coluna' + porcentagemVida][vm.valoresTemp.estadoConservacao.id - valorSubtracao];

                        var valorDepreciacao = 100 - valorTabela;

                        vm.valoresTemp.valorCubDepreciado = (vm.valoresTemp.valorCub * valorDepreciacao) / 100;

                        var somaMotivos = 0;

                        for (var i = 0; i < vm.listaEstimativas.length; i++) {
                            somaMotivos += vm.listaEstimativas[i].porcentagem;

                        }
                        var area = vm.benfeitoria ? vm.benfeitoria.areaConstruida : vm.unidadeAutonomaImovel[0].area
                        vm.valoresTemp.valor = ((1 + (somaMotivos / 100)) * vm.valoresTemp.valorCubDepreciado) * area;

                    } else {
                        vm.valoresTemp.valorCubDepreciado = 0;
                    }
                }
            }
        };

        function arredondaNumero(porcentagemVida) {
            var valor = 2 * Math.round(porcentagemVida / 2);

            if (valor < 2) {
                return 2;
            } else if (valor > 100) {
                return 100;
            } else if (valor % 2 !== 0) {
                return valor + 1;
            } else {
                return valor;
            }

        }

        function verificaIdadeAparente() {
            if (vm.valoresTemp.idadeAparente > vm.valoresTemp.vidaUtil) {
                vm.valoresTemp.vidaUtil = '';
                vm.valoresTemp.idadeAparente = '';
                ComumFactory.mostrarMensagemError('A idade aparente do imóvel não pode ser maior que a vida útil.');
                return false;
            } else if (vm.valoresTemp.vidaUtil === 0) {
                vm.valoresTemp.vidaUtil = '';
                ComumFactory.mostrarMensagemError('A Vida Útil não pode ser igual a 0');
                return false;
            }
            return true;
        }

        vm.paginarEstimativas = function () {
            vm.totalItems = vm.listaEstimativas.length;

            vm.paginacaoEstimativasValores = [];
            vm.totalItems = vm.listaEstimativas.length;
            if (vm.totalItems < vm.tamanhoLimite) {
                vm.paginacaoEstimativasValores = vm.listaEstimativas;
            } else {
                for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                    vm.paginacaoEstimativasValores.push(vm.listaEstimativas[i]);
                    if (vm.paginacaoEstimativasValores.length == vm.tamanhoLimite) {
                        break;
                    }
                }
            }
        };

        vm.limparDadosFatoresAcrescimo = function () {
            vm.listaEstimativas = [];
            vm.paginarEstimativas();
        };

        vm.fecharEstimativasValor = function () {
            voltarCadastrarLaudoAvaliacao();
        };

        vm.gravarValoresCub = function (formularioDados) {
            vm.validarFormulario(formularioDados);

            if (!formularioDados.$valid) {
                return;
            }

            if (vm.tipoTerreno === vm.TIPO_BENFEITORIA) {
                vm.benfeitoria.vidaUtil = vm.valoresTemp.vidaUtil;
                vm.benfeitoria.idadeAparente = vm.valoresTemp.idadeAparente;
                vm.benfeitoria.valorCub = vm.valoresTemp.valorCub;
                vm.benfeitoria.valorCubDepreciado = vm.valoresTemp.valorCubDepreciado;
                vm.benfeitoria.valor = vm.valoresTemp.valor;
                vm.benfeitoria.estimativas = vm.listaEstimativas;
                vm.benfeitoria.isFatoresAcrescimo = vm.valoresTemp.isFatoresAcrescimo;
                vm.benfeitoria.estadoConservacao = vm.valoresTemp.estadoConservacao;
                vm.benfeitoria.padraoAcabamento = vm.valoresTemp.cubSelecionado.padraoAcabamento;
                vm.benfeitoria.tipoProjetoCub = vm.valoresTemp.cubSelecionado.tipoProjetoCub;

                angular.forEach(vm.listaBenfeitoriasImovel, function (benfeitoriaImovel) {
                    if (benfeitoriaImovel.id === vm.benfeitoria.id) {
                        benfeitoriaImovel = angular.copy(vm.benfeitoria);
                    }
                });
            } else if (vm.tipoTerreno === vm.TIPO_UNIDADE_AUTONOMA) {
                vm.unidadeAutonomaImovel[0].vidaUtil = vm.valoresTemp.vidaUtil;
                vm.unidadeAutonomaImovel[0].idadeAparente = vm.valoresTemp.idadeAparente;
                vm.unidadeAutonomaImovel[0].valorCub = vm.valoresTemp.valorCub;
                vm.unidadeAutonomaImovel[0].valorCubDepreciado = vm.valoresTemp.valorCubDepreciado;
                vm.unidadeAutonomaImovel[0].valor = vm.valoresTemp.valor;
                vm.unidadeAutonomaImovel[0].estimativas = vm.listaEstimativas;
                vm.unidadeAutonomaImovel[0].isFatoresAcrescimo = vm.valoresTemp.isFatoresAcrescimo;
                vm.unidadeAutonomaImovel[0].estadoConservacao = vm.valoresTemp.estadoConservacao;
                vm.unidadeAutonomaImovel[0].padraoAcabamento = vm.valoresTemp.cubSelecionado.padraoAcabamento;
                vm.unidadeAutonomaImovel[0].tipoProjetoCub = vm.valoresTemp.cubSelecionado.tipoProjetoCub;
            }

            voltarCadastrarLaudoAvaliacao();
        };

        var voltarCadastrarLaudoAvaliacao = function () {
            vm.laudoAvaliacao.unidadeAutonomaIncorporacao = vm.unidadeAutonomaImovel;
            avaliacaoEscopoCompartilhadoService.setObjetos('rip', vm.rip);
            avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('laudoAvaliacao', vm.laudoAvaliacao);
            avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriasImovel', vm.listaBenfeitoriasImovel);
            avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaImovel', vm.unidadeAutonomaImovel);

            $state.go('avaliacao.CADASTRAR_RELATORIO_VALOR_REFERENCIA', {
                'rip': vm.rip
            });
        };

        vm.showDialogDepreciacaoHeidecke = function (ev) {
            $mdDialog.show({
                controller: function () {

                    vm.downloadPlanilha = function(){
                        window.open('assets/docs/Tab_Ross.pdf', "_blank");
                    }

                    return vm;
                },
                controllerAs: 'vm',
                templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDepreciacaoHeidecke.tmpl.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose: true,
                fullscreen: true
            });
        };

        vm.cancelModal = function () {
            $mdDialog.cancel();
        };

        vm.limparEstimativaValorCub = function () {
            vm.estimativa = {};
        };

        function populaTabelaDepreciacao() {
            vm.tabelaDepreciacao = {
                coluna2: [1.02, 1.05, 3.51, 9.03, 18.9, 33.9, 53.1, 75.4],
                coluna4: [2.08, 2.11, 4.55, 10, 19.8, 34.6, 53.6, 75.7],
                coluna6: [3.18, 3.21, 5.62, 11, 20.7, 35.3, 54.1, 76],
                coluna8: [4.32, 4.35, 6.73, 12.1, 21.6, 36.1, 54.6, 76.3],
                coluna10: [5.5, 5.53, 7.88, 13.2, 22.6, 36.9, 55.2, 76.6],
                coluna12: [6.72, 6.75, 9.07, 14.3, 23.6, 37.7, 55.8, 76.9],
                coluna14: [7.98, 8.01, 10.3, 15.4, 24.6, 38.5, 56.4, 77.2],
                coluna16: [9.28, 9.31, 11.6, 16.6, 25.7, 39.4, 57, 77.5],
                coluna18: [10.6, 10.6, 12.9, 17.8, 26.8, 40.3, 57.6, 77.8],
                coluna20: [12, 12, 14.2, 19.1, 27.9, 42.2, 58.3, 78.2],
                coluna22: [13.4, 13.4, 15.6, 20.4, 29.1, 42.2, 59, 78.5],
                coluna24: [14.9, 14.9, 17, 21.8, 30.3, 43.1, 59.6, 78.9],
                coluna26: [16.4, 16.4, 18.5, 23.1, 31.5, 44.1, 60.4, 79.3],
                coluna28: [17.9, 17, 20, 24.6, 32.8, 45.2, 61.1, 79.6],
                coluna30: [19.5, 19.5, 21.5, 26, 34.1, 46.2, 61.8, 80],
                coluna32: [21.1, 21.1, 23.1, 27.5, 35.4, 47.3, 62.6, 80.4],
                coluna34: [22.8, 22.8, 24.7, 29, 36.8, 48.4, 63.4, 80.8],
                coluna36: [24.5, 24.5, 26.4, 30.5, 38.1, 49.5, 64.2, 81.3],
                coluna38: [26.2, 26.2, 28.1, 32.2, 39.6, 50.7, 65, 81.7],
                coluna40: [28.8, 28.8, 29.9, 33.8, 41, 51.9, 65.9, 82.1],
                coluna42: [29.8, 29.8, 31.6, 35.5, 42.5, 53.1, 66.7, 82.6],
                coluna44: [31.7, 31.7, 33.4, 37.2, 44, 54.4, 67.6, 83.1],
                coluna46: [33.6, 33.6, 35.2, 38.9, 45.6, 55.6, 68.5, 83.5],
                coluna48: [35.5, 35.5, 37.1, 40.7, 47.2, 56.9, 69.4, 84],
                coluna50: [37.5, 37.5, 39.1, 42.6, 48.8, 58.2, 70.4, 84.5],
                coluna52: [39.5, 39.5, 41.9, 44, 50.5, 59.6, 71.3, 85],
                coluna54: [41.6, 41.6, 43, 46.3, 52.1, 61, 72.3, 85.5],
                coluna56: [43.7, 43.7, 45.1, 48.2, 53.9, 62.4, 63.3, 86],
                coluna58: [45.8, 45.8, 47.2, 50.2, 55.6, 63.8, 74.3, 86.6],
                coluna60: [48.8, 48.8, 49.3, 52.2, 57.4, 65.3, 75.3, 87.1],
                coluna62: [50.2, 50.2, 51.5, 54.2, 59.2, 66.7, 75.4, 87.7],
                coluna64: [52.5, 52.5, 53.7, 56.3, 61.1, 61.3, 77.5, 88.2],
                coluna66: [54.8, 54.8, 55.9, 58.4, 69, 69.8, 78.6, 88.8],
                coluna68: [57.1, 57.1, 58.2, 60.6, 64.9, 71.4, 79.7, 89.4],
                coluna70: [59.5, 59.5, 60.5, 62.8, 66.8, 72.9, 80.8, 90.8],
                coluna72: [62.2, 61.9, 62.9, 85, 68.8, 74.6, 81.9, 90.6],
                coluna74: [64.4, 64.4, 65.3, 67.3, 70.8, 76.2, 83.1, 91.2],
                coluna76: [66.9, 66.9, 67.7, 69.6, 72.9, 77.9, 84.3, 91.8],
                coluna78: [69.4, 69.4, 72.7, 71.9, 74.9, 89.6, 85.5, 92.4],
                coluna80: [72, 72, 72.7, 74.3, 77.1, 81.3, 86.7, 93.1],
                coluna82: [74.6, 74.6, 75.3, 76.7, 79.2, 83, 88, 93.7],
                coluna84: [77.3, 77.3, 77.8, 79.1, 81.4, 84.8, 89.2, 94.4],
                coluna86: [80, 80, 80.5, 81.6, 83.6, 86.6, 90.5, 95],
                coluna88: [82.7, 82.7, 83.2, 84.1, 85.8, 88.5, 91.8, 95.7],
                coluna90: [85.5, 85.5, 85.9, 86.7, 88.1, 90.3, 93.1, 96.4],
                coluna92: [88.3, 83.3, 88.6, 89.3, 90.4, 92.7, 94.5, 97.1],
                coluna94: [91.2, 91.2, 91.4, 91.9, 92.8, 94.1, 95.8, 97.8],
                coluna96: [94.1, 94.1, 94.2, 94.6, 95.1, 96, 97.2, 98.5],
                coluna98: [97, 97, 97.1, 97.3, 97.6, 98, 98, 99.8],
                coluna100: [100, 100, 100, 100, 100, 100, 100, 100]
            };
        }

        init();
    }]);
}());
(function(){
    'use strict';

    angular.module('su-avaliacao')
        .factory('pvgSpuService', ["$http", function ($http) {

            var URL_PVG_SPU = '/avaliacao/api/pvg-spu';

            var salvarAvaliacaoPvgSpu = function(pvgSpu) {
                return $http.post(URL_PVG_SPU, pvgSpu);
            };

            var buscarHistorioAvaliacaoPvgSpu = function(rip, offset, limit) {
                return $http.get(URL_PVG_SPU + '/buscar/historico/pvg/spu', {
                    params: {
                        rip: rip,
                        offset: offset,
                        limit: limit
                    }
                });
            };

            var verficarAvaliacoesPvgSpu = function(rip, offset, limit) {
                return $http.get(URL_PVG_SPU + '/verificar/avaliacoes/pvg/spu', {
                    params: {
                        rip: rip,
                        offset: offset,
                        limit: limit
                    }
                });
            };

            var cancelarAvaliacaoPvgSpu = function(pvgSpu) {
                return $http.put(URL_PVG_SPU, pvgSpu);
            };

            var desvincularAvaliacaoPvgSpu = function(pvgSpu) {
                return $http.put(URL_PVG_SPU + '/desvincular/trecho', pvgSpu);
            };

            var consultarPorIdPvgSpu = function(idPvgSpu) {
                return $http.get(URL_PVG_SPU + '/consultar/' + idPvgSpu);
            };

            var recuperarDocumentoSei = function(num) {
                return $http.get(URL_PVG_SPU + '/recuperar-documento-sei/' + num);
            };

            var consultarTrechosSelecionados = function(rip) {
                return $http.get(URL_PVG_SPU + '/consultar/trechos/' + rip);
            };

            var excluirAvaliacaoPvgSpu = function (idPvgSpu) {
                return $http.delete(URL_PVG_SPU + '/excluir-avaliacao-pvg-spu/' + idPvgSpu);
            };


            return {
                salvarAvaliacaoPvgSpu: salvarAvaliacaoPvgSpu,
                buscarHistorioAvaliacaoPvgSpu: buscarHistorioAvaliacaoPvgSpu,
                cancelarAvaliacaoPvgSpu: cancelarAvaliacaoPvgSpu,
                consultarPorIdPvgSpu: consultarPorIdPvgSpu,
                consultarTrechosSelecionados: consultarTrechosSelecionados,
                desvincularAvaliacaoPvgSpu: desvincularAvaliacaoPvgSpu,
                verficarAvaliacoesPvgSpu: verficarAvaliacoesPvgSpu,
                recuperarDocumentoSei: recuperarDocumentoSei,
                excluirAvaliacaoPvgSpu: excluirAvaliacaoPvgSpu
            }
        }]);

}());

(function(){
	'use strict';

	angular.module('su-avaliacao')
		.factory('pvgAvaliacaoService', ["$http", function ($http) {

	        var URL_PVG = '/avaliacao/api/pvg';

	        var salvarPvg = function(pvg) {
				return $http.post(URL_PVG, pvg);
			};

			var buscarPvgsMunicipio = function(rip, offset, limit) {
				return $http.get(URL_PVG + '/municipio', {
					params: {
						rip: rip,
						offset: offset,
						limit: limit
					}
				});
			};

			var buscarPvgsIncra = function(rip, offset, limit) {
				return $http.get(URL_PVG + '/incra', {
					params: {
						rip: rip,
						offset: offset,
						limit: limit
					}
				});
			};

			var consultarPorId = function(idPvg) {
				return $http.get(URL_PVG + '/' + idPvg);
			};

			var cancelarPvg = function(idPvg) {
				return $http.put(URL_PVG + '/cancelar', idPvg);
			};

			var verificarDatas = function(rip, tipo, dtinicio) {
				var pvg = {};
				pvg.ripImovel = rip;
				pvg.tipo = tipo;
				pvg.dataInicial = dtinicio;
				return $http.post(URL_PVG + '/verificar-datas', pvg);
			};

            var excluirAvaliacaoPvg = function (idPvg) {
                return $http.delete(URL_PVG + '/excluir-avaliacao-pvg/' + idPvg);
            };

	        return {
				salvarPvg: salvarPvg,
				buscarPvgsMunicipio: buscarPvgsMunicipio,
				buscarPvgsIncra: buscarPvgsIncra,
				consultarPorId: consultarPorId,
				cancelarPvg: cancelarPvg,
                verificarDatas: verificarDatas,
                excluirAvaliacaoPvg: excluirAvaliacaoPvg
	        }
	}]);

}());

(function(){
	'use strict';

	angular.module('su-avaliacao')
		.factory('dominioPlenoService', ["$http", function ($http) {

	        var URL_BASE = '/avaliacao/api/dominio-pleno';


			var buscarValorDominioPleno = function(rip, offset, limit) {
				return $http.get(URL_BASE, {
					params: {
						rip: rip,
						offset: offset,
						limit: limit
					}
				});
			};

            var buscarValorDominioPlenoPorData = function(ripImovel, data) {
                return $http.get(URL_BASE + '/buscar-valor-terreno', {
                    params: {
                        rip: ripImovel,
                        data: data
                    }
                });
            };

            var buscarValorDominioPlenoVigente = function(ripImovel) {
                return $http.get(URL_BASE + '/buscar-dominio-pleno-vigente', {
                    params: {
                        rip: ripImovel
                    }
                });
            };

	        return {
                buscarValorDominioPleno: buscarValorDominioPleno,
                buscarValorDominioPlenoVigente: buscarValorDominioPlenoVigente,
                buscarValorDominioPlenoPorData: buscarValorDominioPlenoPorData
	        }
	}]);

}());

(function () {
    'use strict';
    angular.module('su-avaliacao')
        .controller('detalharImovelContabilidadeController', ["$scope", "$stateParams", "$timeout", "imovelAvaliacaoService", "imovelContabilidadeService", "$mdToast", "$mdDialog", "ComumFactory", "$filter", "$state", "$mdMedia", "avaliacaoEscopoCompartilhadoService", "dominioAvaliacaoService", function ($scope, $stateParams, $timeout, imovelAvaliacaoService, imovelContabilidadeService,
                                                                       $mdToast, $mdDialog, ComumFactory, $filter, $state, $mdMedia, avaliacaoEscopoCompartilhadoService, dominioAvaliacaoService) {
            var vm = $scope;

            $scope.imovel = {};
            $scope.fotosImovel = [];
            $scope.areaTerreno = 0;

            vm._ = _;
            vm.imagens = [];
            vm.slides = [];
            vm.slickConfig1Loaded = true;
            vm.terreno = {};
            $scope.albumFotos = [];
            vm.isConsultarRip = false;
            vm.showAvisoMoeda = false;
            vm.pesquisaHistorico = {};
            vm.pesquisaHistorico.tipo = 'tabela';
            vm.pesquisaHistorico.dataInicial = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
            vm.pesquisaHistorico.dataFinal = new Date();
            vm.pesquisaDepreciacao = {};
            vm.pesquisaDepreciacao.tipo = 'tabela';
            vm.pesquisaDepreciacao.modo = 'acumulada';
            vm.pesquisaDepreciacao.dataInicial = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
            vm.pesquisaDepreciacao.dataFinal = new Date();

            var init = function () {
                if ($stateParams.ripDetalhar) {
                    $scope.rip = $stateParams.ripDetalhar;
                    vm.ripConsultar = $stateParams.ripDetalhar;
                    vm.pesquisaHistorico.rip = $stateParams.ripDetalhar;
                    vm.pesquisaDepreciacao.rip = $stateParams.ripDetalhar;
                    imovelAvaliacaoService.consultarImovelPorRip($scope.rip).then(function (resposta) {
                        $scope.imovel = resposta.data;

                        definirAreaTerreno();
                    });
                } else if ($stateParams.rip) {
                    $scope.rip = $stateParams.rip;
                    vm.ripConsultar = $stateParams.rip;
                    vm.pesquisaHistorico.rip = $stateParams.rip;
                    vm.pesquisaDepreciacao.rip = $stateParams.rip;
                    imovelAvaliacaoService.consultarImovelPorRip($scope.rip).then(function (resposta) {
                        $scope.imovel = resposta.data;

                        definirAreaTerreno();
                    });
                }
                setTimeout(function () {
                    vm.buscarHistorico();
                }, 100);

                vm.ambienteDev = dominioAvaliacaoService.ehAmbienteDesenvolvimento();
            };

            vm.buscarHistorico = function () {
                if ($scope.formPesquisaHistorico.$valid) {
                    imovelContabilidadeService.buscarHistorico(vm.pesquisaHistorico).then(function (resposta) {
                        vm.dadosContabeis = {};
                        vm.dadosContabeis.tipo = vm.pesquisaHistorico.tipo;
                        vm.dadosContabeis.data = _.reverse(resposta.data);

                        var benfeitoria = !!(vm.dadosContabeis.data.length && vm.dadosContabeis.data[0].benfeitorias);

                        vm.dadosContabeis.header = resposta.data.map(function (item) {
                            return $filter('date')(item.dataAtualizacao, 'dd/MM/yyyy');
                        });

                        for (var i = 0; i < vm.dadosContabeis.data.length; i++) {
                            if (vm.dadosContabeis.data[0].moeda.id !== vm.dadosContabeis.data[i].moeda.id) {
                                vm.showAvisoMoeda = true;
                                break;
                            }
                        }

                        vm.dadosContabeis.series = [
                            {
                                name: 'Valor Total',
                                data: resposta.data.map(function (item) {
                                    return parseFloat(item.valor).toFixed(2);
                                })
                            }
                        ];

                        if (benfeitoria) {
                            vm.dadosContabeis.series.push({
                                name: 'Valor Terreno',
                                data: resposta.data.map(function (item) {
                                    return parseFloat(item.valorTerreno).toFixed(2);
                                })
                            });

                            for (var i = 0; i < vm.dadosContabeis.data[0].benfeitorias.length; i++) {
                                vm.dadosContabeis.series.push({
                                    name: 'Benfeitoria [' + vm.dadosContabeis.data[0].benfeitorias[i].codigo + ']',
                                    data: resposta.data.map(function (item) {
                                        return parseFloat(item.benfeitorias[i].valor).toFixed(2);
                                    })
                                });
                            }

                            vm.buscarDepreciacao();
                        } else {
                            vm.dadosContabeis.series.push({
                                name: 'Valor da Fração Ideal do Terreno',
                                data: resposta.data.map(function (item) {
                                    return parseFloat(item.valorContabilUnidadeAutonoma).toFixed(2);
                                })
                            });
                            vm.dadosContabeis.series.push({
                                name: 'Valor da Unidade Autônoma',
                                data: resposta.data.map(function (item) {
                                    return parseFloat(item.valorContabilUnidadeAutonoma).toFixed(2);
                                })
                            });
                        }

                        setTimeout(function () {
                            createChartContabil(vm.dadosContabeis.series, vm.dadosContabeis.header);
                        }, 200);
                    });
                }
            };

            vm.buscarDepreciacao = function () {
                if (!$scope.formPesquisaDepreciacao || $scope.formPesquisaDepreciacao.$valid) {
                    imovelContabilidadeService.buscarDepreciacao(vm.pesquisaDepreciacao).then(function (resposta) {
                        vm.dadosDepreciacao = {};
                        vm.dadosDepreciacao.data = resposta.data;
                        vm.dadosDepreciacao.tipo = vm.pesquisaDepreciacao.tipo;
                        vm.dadosDepreciacao.modo = vm.pesquisaDepreciacao.modo;

                        if (resposta.data.length > 0) {
                            var benfeitoria = resposta.data[0].benfeitorias ? true : false;

                            vm.dadosDepreciacao.header = resposta.data.map(function (item) {
                                if (benfeitoria) {
                                    return item.benfeitorias.map(function (item2) {
                                        return $filter('date')(item2.dtAtualizacao, 'MM/yyyy');
                                    });
                                } else {
                                    return item.unidadeAutonoma.map(function (item2) {
                                        return $filter('date')(item2.dtAtualizacao, 'MM/yyyy');
                                    });
                                }
                            });
                            vm.dadosDepreciacao.header = _.flattenDeep(vm.dadosDepreciacao.header);
                            vm.dadosDepreciacao.header = _.sortedUniq(vm.dadosDepreciacao.header);
                            vm.dadosDepreciacao.header = _.reverse(vm.dadosDepreciacao.header);

                            for (var j = 0; j < vm.dadosDepreciacao.data.length; j++) {
                                if (benfeitoria) {
                                    while (vm.dadosDepreciacao.data[j].benfeitorias.length < vm.dadosDepreciacao.header.length) {
                                        vm.dadosDepreciacao.data[j].benfeitorias.push({});
                                    }
                                } else {
                                    while (vm.dadosDepreciacao.data[j].unidadeAutonoma.length < vm.dadosDepreciacao.header.length) {
                                        vm.dadosDepreciacao.data[j].unidadeAutonoma.push({});
                                    }
                                }
                            }

                            vm.dadosDepreciacao.data = resposta.data.map(function (item) {
                                if (benfeitoria) {
                                    return _.reverse(item.benfeitorias.map(function (item2) {
                                        return item2;
                                    }));
                                } else {
                                    return _.reverse(item.unidadeAutonoma.map(function (item2) {
                                        return item2;
                                    }));
                                }
                            });

                            var dataChart = vm.dadosDepreciacao.data.map(function (item) {
                                return {
                                    name: benfeitoria ? 'Benfeitoria [' + item[0].codigo + ']' : 'Unidade Autônoma',
                                    data: item.map(function (item1) {
                                        return vm.dadosDepreciacao.modo == 'acumulada' ? parseFloat(item1.depreciacaoAcumulada.toFixed(2)) || null : parseFloat((item1.depreciacaoMensal || 0).toFixed(2)) || null;
                                    })
                                }
                            });

                            setTimeout(function () {
                                createChartDepreciacao(dataChart, vm.dadosDepreciacao.header);
                            }, 200);
                        }
                    });
                }
            };

            vm.consultarImovelPeloRipInformado = function () {

                if (vm.ripConsultar && vm.ripConsultar != '') {
                    this.ripConsultar = ("00000000" + this.ripConsultar).slice(-8);
                    imovelAvaliacaoService.consultarImovelPorRip(vm.ripConsultar).then(function () {
                        $state.go('avaliacao.CONSULTAR_DETALHAR_IMOVEL_CONTABILIDADE', {
                            rip: vm.ripConsultar
                        });
                    }, function () {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-imovel-nao-encontrado'));
                        vm.ripConsultar = null;
                    });
                } else {
                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-rip-invalido'));
                    vm.ripConsultar = null;
                }
            };

            vm.changeMoeda = function () {
                vm.pesquisaHistorico.dataFinal = new Date(vm.pesquisaHistorico.moedaDataAtualizacao);
                vm.buscarHistorico();
            };

            vm.redirecionarConsultarImovelLaudo = function () {
                vm.tela = 'CONSULTAR_DETALHAR_IMOVEL_CONTABILIDADE';
                avaliacaoEscopoCompartilhadoService.setObjetos('tela', vm.tela);
                $state.go('avaliacao.CONSULTAR_IMOVEL_LAUDO');
            };

            vm.gotoLaudo = function (laudoId) {
                $state.go('avaliacao.DETALHAR_LAUDO_AVALIACAO', {rip: vm.ripConsultar, idLaudo: laudoId});
            };

            vm.gotoAvaliacao = function () {
                $state.go('avaliacao.CONSULTAR_DETALHAR_IMOVEL_AVALIACAO', {rip: vm.ripConsultar});
            };

            vm.gotoContabilidade = function () {
                $state.go('avaliacao.CONSULTAR_DETALHAR_IMOVEL_CONTABILIDADE', {rip: vm.ripConsultar});
            };

            vm.ripImovelLink = function (rip) {
                window.open('#/incorporacao/detalharImovel/' + rip, '_blank');
            };

            vm.dadosUtilizacaoLink = function (rip) {
                window.open('#/avaliacao/apresentarDadosUtilizacao/' + rip, '_blank');
            };

            var definirAreaTerreno = function () {
                if ($scope.imovel.proprietarioTitular) {
                    if (($scope.imovel.proprietarioTitular.idProprietarioReal && $scope.imovel.proprietarioTitular.idProprietarioReal != '1')
                        || !$scope.imovel.proprietarioTitular.uniaoParcialmente) {
                        /*area total*/
                        if ($scope.imovel.terreno.areaTotalCadastral != 0 &&
                            $scope.imovel.terreno.areaTotalCartorial != 0) {
                            $scope.areaTerreno = $scope.imovel.terreno.areaTotalCartorial;
                        } else {
                            $scope.areaTerreno = $scope.imovel.terreno.areaTotalCadastral;
                        }
                    } else if (($scope.imovel.tipoImovel.descricao == 'Unidade autônoma' &&
                        !$scope.imovel.proprietarioTitular.uniaoParcialmente) ||
                        (($scope.imovel.proprietarioTitular.idProprietarioReal && $scope.imovel.proprietarioTitular.idProprietarioReal != '1'))) {
                        /*Fração Ideal do Imóvel*/
                        if ($scope.imovel.terreno.areaTotalCadastral != 0 &&
                            $scope.imovel.terreno.areaTotalCartorial != 0) {
                            $scope.areaTerreno = $scope.imovel.terreno.areaTotalCartorial;
                        } else {
                            $scope.areaTerreno = $scope.imovel.terreno.areaTotalCadastral;
                        }
                    } else if ($scope.imovel.proprietarioTitular.uniaoParcialmente &&
                        $scope.imovel.tipoImovel.descricao != 'Unidade autônoma') {
                        /*Area da Uniao*/
                        if ($scope.imovel.terreno.areaUniaoCadastral != 0 &&
                            $scope.imovel.terreno.areaUniaoCartorial != 0) {
                            $scope.areaTerreno = $scope.imovel.terreno.areaUniaoCartorial;
                        } else {
                            $scope.areaTerreno = $scope.imovel.terreno.areaUniaoCadastral;
                        }
                    } else if ($scope.imovel.proprietarioTitular.uniaoParcialmente &&
                        $scope.imovel.tipoImovel.descricao == 'Unidade autônoma') {
                        /*Fração Ideal da União*/
                        if ($scope.imovel.terreno.areaUniaoCadastral != 0 &&
                            $scope.imovel.terreno.areaUniaoCartorial != 0) {
                            $scope.areaTerreno = $scope.imovel.terreno.areaUniaoCartorial;
                        } else {
                            $scope.areaTerreno = $scope.imovel.terreno.areaUniaoCadastral;
                        }
                    }
                }
            };

            vm.dialogDetalhesBenfeitoriaDepreciacao = function (ev, benfeitoria) {
                var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                $mdDialog.show({
                    controller: ["$scope", function ($scope) {
                        $scope.benfeitoria = benfeitoria;
                        $scope.fecharDialog = function () {
                            $mdDialog.cancel();
                        };
                    }],
                    templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesBenfeitoriaDepreciacao.tmpl.html',
                    parent: angular.element(document.body),
                    targetEvent: ev,
                    clickOutsideToClose: true,
                    fullscreen: useFullScreen
                });
            };

            vm.dialogDetalhesEvento = function (ev, evento) {
                var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                $mdDialog.show({
                    controller: ["$scope", function ($scope) {
                        $scope.evento = evento;
                        $scope.fecharDialog = function () {
                            $mdDialog.cancel();
                        };
                        $scope.getCuteName = function (name) {
                            switch (name) {
                                case 'VALOR_CONTÁBIL':
                                    return 'Atualização sistêmica';
                                case 'DOMÍNIO_PLENO':
                                    return 'Atualização do valor do domínio pleno';
                                case 'LAUDO_AVALIACAO-VIGENTE':
                                    return 'Laudo de avaliação';
                                case 'LAUDO_AVALIACAO-ATIVO':
                                    return 'Laudo de avaliação';
                                case 'LAUDO_RELATORIO_VALOR_REFERENCIA':
                                    return 'Relatório de valor de Referência';
                            }
                        };
                    }],
                    templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesEvento.tmpl.html',
                    parent: angular.element(document.body),
                    targetEvent: ev,
                    clickOutsideToClose: true,
                    fullscreen: useFullScreen
                });
            };

            vm.getCuteName = function (name) {
                switch (name) {
                    case 'VALOR_CONTÁBIL':
                        return 'Atualização sistêmica';
                    case 'DOMÍNIO_PLENO':
                        return 'Atualização do valor do domínio pleno';
                    case 'LAUDO_AVALIACAO-VIGENTE':
                        return 'Laudo de avaliação';
                    case 'LAUDO_AVALIACAO-ATIVO':
                        return 'Laudo de avaliação';
                    case 'LAUDO_RELATORIO_VALOR_REFERENCIA':
                        return 'Relatório de valor de Referência';
                }
            };

            function createChartContabil(data, categorias) {
                $("#chart").kendoChart({
                    legend: {
                        position: "bottom",
                        orientation: "vertical",
                        align: "start"
                    },
                    seriesDefaults: {
                        type: "line"
                    },
                    series: data,
                    valueAxis: {
                        labels: {
                            format: "{0}"
                        },
                        line: {
                            visible: false
                        },
                        axisCrossingValue: -10
                    },
                    categoryAxis: {
                        categories: categorias,
                        majorGridLines: {
                            visible: false
                        },
                        labels: {
                            rotation: "auto"
                        }
                    },
                    tooltip: {
                        visible: true,
                        format: "{0}%",
                        template: "#= series.name #: #= value #"
                    }
                });
            }

            function createChartDepreciacao(data, categorias) {
                $("#chart2").kendoChart({
                    legend: {
                        position: "bottom",
                        orientation: "vertical",
                        align: "start"
                    },
                    seriesDefaults: {
                        type: "line"
                    },
                    series: data,
                    valueAxis: {
                        labels: {
                            format: "{0}"
                        },
                        line: {
                            visible: false
                        },
                        axisCrossingValue: -10
                    },
                    categoryAxis: {
                        categories: categorias,
                        majorGridLines: {
                            visible: false
                        },
                        labels: {
                            rotation: "auto"
                        }
                    },
                    tooltip: {
                        visible: true,
                        format: "{0}%",
                        template: "#= series.name #: #= value #"
                    }
                });
            }

            init();

        }]);
})();

(function () {
    'use strict';
    angular.module('su-avaliacao')
        .controller('detalharImovelAvaliacaoController', ["$scope", "$stateParams", "imovelAvaliacaoService", "usuarioAvaliacaoService", "ComumFactory", "$filter", "$state", "avaliacaoEscopoCompartilhadoService", "dominioAvaliacaoService", "localStorageService", "laudoAvaliacaoService", function ($scope, $stateParams, imovelAvaliacaoService, usuarioAvaliacaoService,
                                                                   ComumFactory, $filter, $state, avaliacaoEscopoCompartilhadoService,
                                                                   dominioAvaliacaoService, localStorageService, laudoAvaliacaoService) {
            var vm = $scope;

            $scope.imovel = {};
            $scope.fotosImovel = [];
            $scope.areaTerreno = 0;

            vm.imagens = [];
            vm.slides = [];
            vm.slickConfig1Loaded = true;
            vm.terreno = {};
            $scope.albumFotos = [];
            vm.isConsultarRip = false;
            vm.ripPreenchido = false;

            vm.dadoContabil = null;

            var init = function () {
                buscarUsuarioLogado(function () {

                    if($stateParams.ripDetalhar)
                        $scope.rip = $stateParams.ripDetalhar;
                    else if($stateParams.rip)
                        $scope.rip = $stateParams.rip;

                    if ($scope.rip) {
                        vm.ripPreenchido = true;
                        vm.ripConsultar = $scope.rip;
                        buscarLaudoContabil();
                        imovelAvaliacaoService.consultarImovelPorRip($scope.rip).then(function (resposta) {
                            $scope.imovel = resposta.data;
                            localStorageService.set('pontoImovel', $scope.imovel);
                            definirAreaTerreno();
                        });
                    }
                });

                vm.ambienteDev = dominioAvaliacaoService.ehAmbienteDesenvolvimento();

            };

            var buscarUsuarioLogado = function (callback) {
                usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                    vm.usuarioLogado = resp.data;
                    if (vm.usuarioLogado.AVALIACAO_CONSULTAR_VISUALIZAR_DADOS_AVALIACAO === 'false' && vm.usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO === 'false') {
                        $state.go('avaliacao.ACESSO_NEGADO');
                    }
                    if (callback) {
                        callback();
                    }
                });

            };

             var buscarLaudoContabil = function () {
                laudoAvaliacaoService.buscarLaudoContabil(vm.ripConsultar).then(function (resp) {
                    vm.dadoContabil = resp.data;
                });
            };

            vm.consultarImovelPeloRipInformado = function () {

                if (vm.ripConsultar && vm.ripConsultar != '') {
                    this.ripConsultar = ("00000000" + this.ripConsultar).slice(-8);
                    imovelAvaliacaoService.consultarImovelPorRip(vm.ripConsultar).then(function () {
                        $state.go('avaliacao.CONSULTAR_DETALHAR_IMOVEL_AVALIACAO', {
                            rip: vm.ripConsultar
                        });
                    }, function () {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-imovel-nao-encontrado'));
                        vm.ripConsultar = null;
                        $state.go('avaliacao.CONSULTAR_DETALHAR_IMOVEL_AVALIACAO', {
                            rip: vm.ripConsultar
                        });
                    });
                } else {
                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-rip-invalido'));
                    vm.ripConsultar = null;
                }
            };

            vm.redirecionarConsultarImovelLaudo = function () {
                vm.tela = 'CONSULTAR_DETALHAR_IMOVEL_AVALIACAO';
                avaliacaoEscopoCompartilhadoService.setObjetos('tela', vm.tela);
                $state.go('avaliacao.CONSULTAR_IMOVEL_LAUDO');
            };

            vm.gotoAvaliacao = function () {
                $state.go('avaliacao.CONSULTAR_DETALHAR_IMOVEL_AVALIACAO', {rip: vm.ripConsultar});
            };

            vm.gotoContabilidade = function () {
                $state.go('avaliacao.CONSULTAR_DETALHAR_IMOVEL_CONTABILIDADE', {rip: vm.ripConsultar});
            };

            vm.ripImovelLink = function (rip) {
                window.open('#/incorporacao/detalharImovel/' + rip, '_blank');
            };

            vm.dadosUtilizacaoLink = function (rip) {
                window.open('#/avaliacao/apresentarDadosUtilizacao/' + rip, '_blank');
            };

            var definirAreaTerreno = function () {
                if ($scope.imovel.proprietarioTitular) {
                    if (($scope.imovel.proprietarioTitular.idProprietarioReal && $scope.imovel.proprietarioTitular.idProprietarioReal != '1')
                        || !$scope.imovel.proprietarioTitular.uniaoParcialmente) {
                        /*area total*/
                        if ($scope.imovel.terreno.areaTotalCadastral != 0 &&
                            $scope.imovel.terreno.areaTotalCartorial != 0) {
                            $scope.areaTerreno = $scope.imovel.terreno.areaTotalCartorial;
                        } else {
                            $scope.areaTerreno = $scope.imovel.terreno.areaTotalCadastral;
                        }
                    } else if (($scope.imovel.tipoImovel.descricao == 'Unidade autônoma' &&
                        !$scope.imovel.proprietarioTitular.uniaoParcialmente) ||
                        (($scope.imovel.proprietarioTitular.idProprietarioReal && $scope.imovel.proprietarioTitular.idProprietarioReal != '1'))) {
                        /*Fração Ideal do Imóvel*/
                        if ($scope.imovel.terreno.areaTotalCadastral != 0 &&
                            $scope.imovel.terreno.areaTotalCartorial != 0) {
                            $scope.areaTerreno = $scope.imovel.terreno.areaTotalCartorial;
                        } else {
                            $scope.areaTerreno = $scope.imovel.terreno.areaTotalCadastral;
                        }
                    } else if ($scope.imovel.proprietarioTitular.uniaoParcialmente &&
                        $scope.imovel.tipoImovel.descricao != 'Unidade autônoma') {
                        /*Area da Uniao*/
                        if ($scope.imovel.terreno.areaUniaoCadastral != 0 &&
                            $scope.imovel.terreno.areaUniaoCartorial != 0) {
                            $scope.areaTerreno = $scope.imovel.terreno.areaUniaoCartorial;
                        } else {
                            $scope.areaTerreno = $scope.imovel.terreno.areaUniaoCadastral;
                        }
                    } else if ($scope.imovel.proprietarioTitular.uniaoParcialmente &&
                        $scope.imovel.tipoImovel.descricao == 'Unidade autônoma') {
                        /*Fração Ideal da União*/
                        if ($scope.imovel.terreno.areaUniaoCadastral != 0 &&
                            $scope.imovel.terreno.areaUniaoCartorial != 0) {
                            $scope.areaTerreno = $scope.imovel.terreno.areaUniaoCartorial;
                        } else {
                            $scope.areaTerreno = $scope.imovel.terreno.areaUniaoCadastral;
                        }
                    }
                }
            };

            init();

        }]);
})();

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('calcularAreaEquivalenteController',
            ["$scope", "avaliacaoEscopoCompartilhadoService", "arquivoAvaliacaoService", "formularioAvaliacaoService", "numeroExtensoAvaliacaoService", "cubAvaliacaoService", "$state", "ComumFactory", "$filter", "$mdDialog", function ($scope, avaliacaoEscopoCompartilhadoService, arquivoAvaliacaoService, formularioAvaliacaoService, numeroExtensoAvaliacaoService, cubAvaliacaoService, $state, ComumFactory, $filter, $mdDialog) {

                var vm = $scope;
                vm.areaEquivalente = {};
                vm.editar = {};
                vm.editar.buttonShow = false;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                var init = function () {
                    recuperarPropriedadesLaudoRelatorio();
                    preencheTiposCub();

                    vm.areaEquivalente = angular.copy(vm.benfeitoria.areaEquivalente);
                    if (vm.areaEquivalente && vm.areaEquivalente.cubPadrao) {
                        buscarValoresCubSiapa(vm.areaEquivalente.cubPadrao, function (valorCub) {
                            vm.valorCubPadrao = valorCub;
                        });

                        vm.tiposCubPadraoDiferente = angular.copy(vm.tiposCub);

                        for (var i = 0; i < vm.tiposCubPadraoDiferente.length; i++) {
                            if (vm.tiposCubPadraoDiferente[i].nome === vm.areaEquivalente.cubPadrao) {
                                vm.tiposCubPadraoDiferente.splice(i, 1);
                            }
                        }
                    }


                };

                var recuperarPropriedadesLaudoRelatorio = function () {
                    vm.laudoRelatorioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoRelatorioAvaliacao');
                    vm.dadosEnderecamento = avaliacaoEscopoCompartilhadoService.getObjeto('dadosEnderecamento');
                    vm.dadosProprietario = avaliacaoEscopoCompartilhadoService.getObjeto('dadosProprietario');
                    vm.benfeitoria = avaliacaoEscopoCompartilhadoService.getObjeto('benfeitoria');
                    vm.tipoTerreno = avaliacaoEscopoCompartilhadoService.getObjeto('tipoTerreno');

                    /**Identificacao**/
                    vm.ripImovel = avaliacaoEscopoCompartilhadoService.getObjeto('ripImovel');
                    vm.pontoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('pontoImovel');
                    vm.listaResponsaveisTecnicos = avaliacaoEscopoCompartilhadoService.getObjeto('listaResponsaveisTecnicos');
                    vm.listaNormasUtilizadas = avaliacaoEscopoCompartilhadoService.getObjeto('listaNormasUtilizadas');
                    /**Detalher Imovel**/
                    vm.listaMatriculasTranscricao = avaliacaoEscopoCompartilhadoService.getObjeto('listaMatriculasTranscricao');
                    vm.listaCertidoesCartoriais = avaliacaoEscopoCompartilhadoService.getObjeto('listaCertidoesCartoriais');
                    angular.forEach(vm.listaCertidoesCartoriais, function (certidaoCartorial) {
                        certidaoCartorial.arquivo.id = null;
                    });
                    vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                    vm.listaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosImovel');
                    /**Terreno Imovel**/
                    vm.terreno = avaliacaoEscopoCompartilhadoService.getObjeto('terreno');
                    vm.documentosParametrosUrbanisticos = avaliacaoEscopoCompartilhadoService.getObjeto('documentosParametrosUrbanisticos');
                    /**Benfeitorias**/
                    vm.listaBenfeitoriaAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriaAvaliacao');
                    vm.listaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosBenfeitorias');
                    /**Unidade Autonoma**/
                    vm.unidadeAutonomaIncorporacao = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaIncorporacao');
                    vm.listaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosUnidadeAutonoma');
                    /**Vistoria**/
                    vm.vistoriaListaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosImovel');
                    vm.vistoriaListaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosBenfeitorias');
                    vm.vistoriaListaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosUnidadeAutonoma');
                    /**Avaliacao**/
                    vm.avaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao');
                    vm.tipoTerreno = avaliacaoEscopoCompartilhadoService.getObjeto('tipoTerreno');
                    /**Quantificacao custo**/
                    vm.tabSelect = avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect');
                    vm.benfeitoria = avaliacaoEscopoCompartilhadoService.getObjeto('benfeitoriaQuantificacao');
                    vm.detalhar = avaliacaoEscopoCompartilhadoService.getObjeto('detalhar');
                    vm.uf = avaliacaoEscopoCompartilhadoService.getObjeto('uf');

                    vm.formularioIdentificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioIdentificacao');
                    vm.formularioDetalhesImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioDetalhesImovel');
                    vm.formularioTerrenoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioTerrenoImovel');
                    vm.formularioBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('formularioBenfeitorias');
                    vm.formularioUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUnidadeAutonoma');
                    vm.formularioUtilizacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUtilizacao');
                    vm.formularioVistoria = avaliacaoEscopoCompartilhadoService.getObjeto('formularioVistoria');
                    vm.formularioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAvaliacao');
                    vm.formularioResultadosRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosRelatorio');
                    vm.formularioResultadosLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosLaudo');
                    vm.formularioQuantificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacao');
                    vm.formularioQuantificacaoOrcamento = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacaoOrcamento');
                    vm.formularioResultadoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadoAvaliacao');
                    vm.formularioAnexo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAnexo');

                    avaliacaoEscopoCompartilhadoService.limparEscopo();
                };

                var voltarCadastrarLaudoRelatorio = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', vm.tipoTerreno);
                    /**Quantificacao custo**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoriaQuantificacao', vm.benfeitoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

                    $state.go('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: vm.laudoRelatorioAvaliacao.id
                    });
                };

                vm.fecharCalcularAreaEquivalente = function () {
                    voltarCadastrarLaudoRelatorio();
                };

                vm.salvar = function () {
                   vm.benfeitoria.areaEquivalente = angular.copy(vm.areaEquivalente);
                   voltarCadastrarLaudoRelatorio();
                };

                vm.changeAreaConstruidaPadrao = function () {
                    if (vm.areaEquivalente.areaConstruidaPadrao > vm.benfeitoria.area || vm.areaEquivalente.areaConstruidaPadrao <= 0) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-area-construida-padrao-invalida'));
                        vm.areaEquivalente.areaConstruidaPadrao = null;
                        return
                    }
                    vm.areaEquivalente.areaEquivalenteConstrucao = vm.areaEquivalente.areaConstruidaPadrao;
                    if (vm.areaEquivalente.areasPadraoDiferente) {
                        angular.forEach(vm.areaEquivalente.areasPadraoDiferente, function (item) {
                            vm.areaEquivalente.areaEquivalenteConstrucao += item.areaEquivalenteCalculada;
                        });
                    }
                };

                vm.changeTipoCoeficiente = function () {
                    vm.cubPadraoDiferente = null;
                    vm.valorCoeficiente = null;
                    vm.fonteCoeficiente = null;
                };

                vm.changeAreaConstruidaPadraoDiferente = function () {
                    var totalArea = vm.areaEquivalente.areaConstruidaPadrao;
                    angular.forEach(vm.areaEquivalente.areasPadraoDiferente, function (item) {
                        totalArea += item.areaConstruidaPadraoDiferente;
                    });

                    if(vm.editar.buttonShow){
                        totalArea -= vm.areaEquivalente.areasPadraoDiferente[vm.editar.index].areaConstruidaPadraoDiferente;
                    }

                    totalArea += vm.areaConstruidaPadraoDiferente;
                    if (totalArea > vm.benfeitoria.area || vm.areaConstruidaPadraoDiferente <= 0) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-area-construida-padrao-diferente-invalida'));
                        vm.areaConstruidaPadraoDiferente = null;
                        return
                    }
                };

                vm.adicionarArea = function () {
                    if (vm.tipoCoeficiente === 'CALCULAR') {
                        if (!vm.areaConstruidaPadraoDiferente || !vm.descricaoAreaConstruidaPadraoDiferente || !vm.valorCoeficiente || !vm.cubPadraoDiferente || !vm.areaEquivalente.areaConstruidaPadrao || !vm.areaEquivalente.cubPadrao) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                            return
                        }
                    } else if (vm.tipoCoeficiente === 'INFORMAR') {
                        if (!vm.areaConstruidaPadraoDiferente || !vm.descricaoAreaConstruidaPadraoDiferente || !vm.valorCoeficiente || !vm.fonteCoeficiente || !vm.areaEquivalente.areaConstruidaPadrao || !vm.areaEquivalente.cubPadrao) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                            return
                        }
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return
                    }

                    if (!vm.areaEquivalente.areasPadraoDiferente) {
                        vm.areaEquivalente.areasPadraoDiferente = [];
                    }

                    vm.areaEquivalente.areasPadraoDiferente.push({
                        areaConstruidaPadraoDiferente: vm.areaConstruidaPadraoDiferente,
                        descricaoAreaConstruidaPadraoDiferente: vm.descricaoAreaConstruidaPadraoDiferente,
                        tipoCoeficiente: vm.tipoCoeficiente,
                        valorCoeficiente: vm.valorCoeficiente,
                        fonteCoeficiente: vm.fonteCoeficiente,
                        cubPadraoDiferente: vm.cubPadraoDiferente,
                        areaEquivalenteCalculada: (vm.valorCoeficiente * vm.areaConstruidaPadraoDiferente)
                    });

                    vm.areaConstruidaPadraoDiferente = null;
                    vm.cubPadraoDiferente = null;
                    vm.descricaoAreaConstruidaPadraoDiferente = null;
                    vm.tipoCoeficiente = null;
                    vm.valorCoeficiente = null;
                    vm.fonteCoeficiente = null;

                    vm.areaEquivalente.areaEquivalenteConstrucao = vm.areaEquivalente.areaConstruidaPadrao;
                    angular.forEach(vm.areaEquivalente.areasPadraoDiferente, function (item) {
                        vm.areaEquivalente.areaEquivalenteConstrucao += item.areaEquivalenteCalculada;
                    });

                };

                vm.alterarArea = function ($index) {
                    vm.areaConstruidaPadraoDiferente = angular.copy(vm.areaEquivalente.areasPadraoDiferente[$index].areaConstruidaPadraoDiferente);
                    vm.cubPadraoDiferente = angular.copy(vm.areaEquivalente.areasPadraoDiferente[$index].cubPadraoDiferente);
                    vm.tipoCoeficiente = angular.copy(vm.areaEquivalente.areasPadraoDiferente[$index].tipoCoeficiente);
                    vm.valorCoeficiente = angular.copy(vm.areaEquivalente.areasPadraoDiferente[$index].valorCoeficiente);
                    vm.fonteCoeficiente = angular.copy(vm.areaEquivalente.areasPadraoDiferente[$index].fonteCoeficiente);
                    vm.descricaoAreaConstruidaPadraoDiferente = angular.copy(vm.areaEquivalente.areasPadraoDiferente[$index].descricaoAreaConstruidaPadraoDiferente);
                    vm.editar.index = $index;
                    vm.editar.buttonShow = true;
                };

                vm.gravarAlteracaoArea = function () {
                    if (vm.tipoCoeficiente === 'CALCULAR') {
                        if (!vm.areaConstruidaPadraoDiferente || !vm.descricaoAreaConstruidaPadraoDiferente || !vm.valorCoeficiente || !vm.cubPadraoDiferente || !vm.areaEquivalente.areaConstruidaPadrao || !vm.areaEquivalente.cubPadrao) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                            return
                        }
                    } else if (vm.tipoCoeficiente === 'INFORMAR') {
                        if (!vm.areaConstruidaPadraoDiferente || !vm.descricaoAreaConstruidaPadraoDiferente || !vm.valorCoeficiente || !vm.fonteCoeficiente || !vm.areaEquivalente.areaConstruidaPadrao || !vm.areaEquivalente.cubPadrao) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                            return
                        }
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return
                    }

                    vm.areaEquivalente.areasPadraoDiferente.splice(vm.editar.index, 1);

                    vm.areaEquivalente.areasPadraoDiferente.push({
                        areaConstruidaPadraoDiferente: vm.areaConstruidaPadraoDiferente,
                        descricaoAreaConstruidaPadraoDiferente: vm.descricaoAreaConstruidaPadraoDiferente,
                        tipoCoeficiente: vm.tipoCoeficiente,
                        valorCoeficiente: vm.valorCoeficiente,
                        fonteCoeficiente: vm.fonteCoeficiente,
                        cubPadraoDiferente: vm.cubPadraoDiferente,
                        areaEquivalenteCalculada: (vm.valorCoeficiente * vm.areaConstruidaPadraoDiferente)
                    });

                    vm.areaConstruidaPadraoDiferente = null;
                    vm.cubPadraoDiferente = null;
                    vm.descricaoAreaConstruidaPadraoDiferente = null;
                    vm.tipoCoeficiente = null;
                    vm.valorCoeficiente = null;
                    vm.fonteCoeficiente = null;

                    vm.editar.index = null;
                    vm.editar.buttonShow = false;

                    vm.areaEquivalente.areaEquivalenteConstrucao = vm.areaEquivalente.areaConstruidaPadrao;
                    angular.forEach(vm.areaEquivalente.areasPadraoDiferente, function (item) {
                        vm.areaEquivalente.areaEquivalenteConstrucao += item.areaEquivalenteCalculada;
                    });

                };

                vm.removerArea = function ($index) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja remover este item?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        vm.areaEquivalente.areasPadraoDiferente.splice($index, 1);
                        vm.areaEquivalente.areaEquivalenteConstrucao = vm.areaEquivalente.areaConstruidaPadrao;
                        angular.forEach(vm.areaEquivalente.areasPadraoDiferente, function (item) {
                            vm.areaEquivalente.areaEquivalenteConstrucao += item.areaEquivalenteCalculada;
                        });
                    });
                };

                vm.changeCubPadrao = function () {
                    vm.tiposCubPadraoDiferente = angular.copy(vm.tiposCub);

                    for (var i = 0; i < vm.tiposCubPadraoDiferente.length; i++) {
                        if (vm.tiposCubPadraoDiferente[i].nome === vm.areaEquivalente.cubPadrao) {
                            vm.tiposCubPadraoDiferente.splice(i, 1);
                        }
                    }
                    if (verificarJaExisteCubPadrao()) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-cub-area-padrao-igual-informado'));
                        vm.areaEquivalente.cubPadrao = null;
                    } else {
                        buscarValoresCubSiapa(vm.areaEquivalente.cubPadrao, function (valorCub) {
                            vm.valorCubPadrao = valorCub;
                        });
                    }
                };

                var verificarJaExisteCubPadrao = function () {
                    var idExiste = false;
                    if (vm.areaEquivalente.areasPadraoDiferente) {
                        angular.forEach(vm.areaEquivalente.areasPadraoDiferente, function (item) {
                            if (item.cubPadraoDiferente === vm.areaEquivalente.cubPadrao) {
                                idExiste = true;
                            }
                        });
                    }
                    return idExiste;
                };

                var verificarJaExiste = function (cubPadraoDiferente) {
                    var idExiste = false;
                    angular.forEach(vm.areaEquivalente.areasPadraoDiferente, function (item) {
                        if (item.cubPadraoDiferente === cubPadraoDiferente) {
                            idExiste = true;
                        }
                    });
                    return idExiste;
                };

                vm.changeCubPadraoDiferente = function () {

                    buscarValoresCubSiapa(vm.cubPadraoDiferente, function (valorCub) {
                        vm.valorCubPadraoDiferente = valorCub;
                        vm.valorCoeficiente = (vm.valorCubPadraoDiferente / vm.valorCubPadrao);
                    });
                };

                function buscarValoresCubSiapa(tipoCub, callback) {
                    var date = new Date();

                    for (var i = 0; i < vm.tiposCub.length; i++) {
                        if (vm.tiposCub[i].nome === tipoCub) {
                            tipoCub = vm.tiposCub[i];
                        }
                    }

                    cubAvaliacaoService.buscarCubSiapa(vm.uf, date, tipoCub).then(function (resp) {
                        var valorCub = tipoCub.nome === 'CUB Médio' ? resp.data.cubs[0].valorMedio : resp.data.cubs[0].valor;

                        if (resp.data.exibirMensagem) {
                            var msg = 'Não existe CUB cadastrado para o mês/ano solicitado. O valor apresentado é referente a ';
                            msg += resp.data.mes;
                            msg += '/';
                            msg += resp.data.ano;
                            msg += '.';
                            vm.msgErro = msg;
                            ComumFactory.mostrarMensagemSucesso(msg);
                        } else {
                            vm.msgErro = '';
                        }

                        callback(valorCub);
                    }, function () {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-nenhum-valor-encontrado-para-cub'));
                        callback(null);
                    });
                };

                var preencheTiposCub = function (callback) {
                    vm.tiposCub = [];
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Baixo)', 1, 1, 'BAIXO', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Normal)', 1, 2, 'NORMAL', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Alto)', 1, 3, 'ALTO', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Baixo)', 4, 1, 'BAIXO', 'PP4_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Normal)', 4, 2, 'NORMAL', 'PP4_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Baixo)', 2, 1, 'BAIXO', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Normal)', 2, 2, 'NORMAL', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Alto)', 2, 3, 'ALTO', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Normal)', 3, 2, 'NORMAL', 'R16_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Alto)', 3, 3, 'ALTO', 'R16_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PIS, Padrão Residencial, Projeto de interesse social (Baixo)', 10, 1, 'BAIXO', 'PIS_PROJETO_INTERESSE_SOCIAL'));
                    vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Normal)', 5, 2, 'NORMAL', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
                    vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Alto)', 5, 3, 'ALTO', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
                    vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Normal)', 6, 2, 'NORMAL', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Alto)', 6, 3, 'ALTO', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Normal)', 7, 2, 'NORMAL', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Alto)', 7, 3, 'ALTO', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('GI, Padrão Galpão Indústrial, 1 pavimento', 8, 1, 'BAIXO', 'GI_PADRAO_GALPAO_INDUSTRIAL'));
                    vm.tiposCub.push(new TipoCub('RP1Q, Padrão Residencia Popular, 1 pavimento', 9, 1, 'BAIXO', 'RP1Q_PADRAO_RESIDENCIAL_POPULAR_QUARTO'));
                    vm.tiposCub.push(new TipoCub('CUB Médio', null, null, 'CUB_MEDIO', 'CUB_MEDIO'));

                    function TipoCub(nome, tipo, padrao, padraoAcabamento, tipoProjetoCub) {
                        this.nome = nome;
                        this.tipo = tipo;
                        this.padrao = padrao;
                        this.padraoAcabamento = padraoAcabamento;
                        this.tipoProjetoCub = tipoProjetoCub;
                    }

                    if (callback) {
                        callback();
                    }
                };

                vm.showDialogCalculoAreaEquivalente = function (ev) {
                    $mdDialog.show({
                        controller: function () {

                            return vm;
                        },
                        controllerAs: 'vm',
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogCalculoAreaEquivalente.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: true
                    });
                };

                vm.cancelModal = function () {
                    $mdDialog.cancel();
                };

                init();
            }])

})();
(function(){
    'use strict';
    angular.module('su-avaliacao')
        .factory('amostraService', ["$http", "Upload", function ($http, Upload) {

            var URL_AMOSTRA = '/avaliacao/api/amostra';
            var URL_INCORPORACAO = '/avaliacao/api/incorporacao';

            var buscarTiposAmostra = function() {
                return $http.get(URL_AMOSTRA + '/buscar-tipos-amostra');
            };

            var buscarClassificacaoAmostra = function() {
                return $http.get(URL_AMOSTRA + '/buscar-classificacao-amostra');
            };

            var buscarEnderecoPorCep = function(CEP) {
                return $http.get(URL_AMOSTRA + '/buscar-endereco-cep/' + CEP);
            };                       

            var salvar = function(amostra) {
                console.log('amostra');
                console.log(amostra);
                return $http.post(URL_AMOSTRA, amostra);
            };

            var gerarPlanilhaAmostra = function(amostra) {
                return $http.post(URL_AMOSTRA + '/gerar-planilha-amostra', amostra, {responseType: "arraybuffer"});
            };

            var consultarDadosAmostra = function(amostra, offset, limit ,orderBy, order) {
                    return $http.post(URL_AMOSTRA + '/filtro-amostras?page=' + offset + '&size=' + limit + "&sort=" + "situacaoAmostra,ASC" + "&sort=" + order+ "," + orderBy, amostra);
            };

            var consultarDadosUploadAmostra = function(filtro, offset, limit ,orderBy, order) {
                    return $http.post(URL_AMOSTRA + '/filtro-dado-upload-amostra?page=' + offset + '&size=' + limit + "&sort=" + order+ "," + orderBy, filtro);
            };

            var consultarAmostraId = function(idAmostra) {
                return $http.get(URL_AMOSTRA + '/buscar-amostra/' + idAmostra);
            };

            var cancelarAmostra = function(amostra) {
                return $http.post(URL_AMOSTRA + '/cancelar-amostra', amostra);
            };

            var selecionarAmostra = function(amostras) {
                return $http.post(URL_AMOSTRA + '/selecionar-amostras', amostras);
            };

            var allAmostras = function() {
                return $http.get(URL_AMOSTRA + '/all-amostras');
            };

            var benfeitoriaUniao = function(cep) {
                return $http.get(URL_INCORPORACAO + '/benfeitoria-uniao/'+cep);
            };

            var buscarTiposDadoComplementar = function (tipo) {
                return $http.get(URL_AMOSTRA + '/tipo-dado-complementar/' + tipo);
            };

            var buscarLaudosRelatorioVinculado = function(idAmostra) {
                return $http.get(URL_AMOSTRA + '/buscar-laudos-relatorio-vicunlado/' + idAmostra);
            };

            var importarPlanilhaAmostra = function(id) {
                return $http.get(URL_AMOSTRA + '/importar-planilha-amostra/' + id, {responseType: "arraybuffer"});
            };

            var consultarDadosAmostraImportacao = function(id) {
                return $http.get(URL_AMOSTRA + '/all-amostras-importacao/' + id);
            };

            var uploadPlanilhaAmostra = function($file){
                return Upload.upload({
                    url: URL_AMOSTRA + '/upload-planilha-amostra',
                    data: {
                        file: $file
                    },
                });
            };

            var salvarRascunhoAmostraImportada = function(amostras) {
                return $http.post(URL_AMOSTRA + '/salvar-rascunho-amostra-importada', amostras);
            };

            var adicionarBaseDadosAmostra = function(amostras) {
                return $http.post(URL_AMOSTRA + '/adicionar-base-dados-amostra', amostras);
            };

            var excluirAmostra = function(idAmostra) {
                return $http.get(URL_AMOSTRA + '/excluir-amostra/' + idAmostra);
            };

            var excluirDadoUploadAmostra = function(id) {
                return $http.get(URL_AMOSTRA + '/excluir-dado-upload-amostra/' + id);
            };

            return {
                buscarTiposAmostra: buscarTiposAmostra,
                buscarClassificacaoAmostra: buscarClassificacaoAmostra,
                buscarEnderecoPorCep: buscarEnderecoPorCep,
                salvar: salvar,
                consultarDadosAmostra: consultarDadosAmostra,
                consultarAmostraId: consultarAmostraId,
                cancelarAmostra: cancelarAmostra,
                allAmostras:allAmostras,
                benfeitoriaUniao:benfeitoriaUniao,
                selecionarAmostra:selecionarAmostra,
                buscarTiposDadoComplementar:buscarTiposDadoComplementar,
                gerarPlanilhaAmostra:gerarPlanilhaAmostra,
                buscarLaudosRelatorioVinculado:buscarLaudosRelatorioVinculado,
                uploadPlanilhaAmostra:uploadPlanilhaAmostra,
                importarPlanilhaAmostra:importarPlanilhaAmostra,
                consultarDadosAmostraImportacao:consultarDadosAmostraImportacao,
                salvarRascunhoAmostraImportada:salvarRascunhoAmostraImportada,
                adicionarBaseDadosAmostra:adicionarBaseDadosAmostra,
                excluirAmostra:excluirAmostra,
                consultarDadosUploadAmostra:consultarDadosUploadAmostra,
                excluirDadoUploadAmostra:excluirDadoUploadAmostra
            }
        }])               
}());

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('uploadPlanilhaAmostraController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "arquivoAvaliacaoService", "$state", "formularioAvaliacaoService", "$mdDialog", "usuarioAvaliacaoService", "$filter", "amostraService", "ComumFactory", "avaliacaoEscopoCompartilhadoService", function ($scope, laudoAvaliacaoService, $stateParams, arquivoAvaliacaoService, $state, formularioAvaliacaoService, $mdDialog, usuarioAvaliacaoService,
                      $filter, amostraService, ComumFactory, avaliacaoEscopoCompartilhadoService) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;
                vm.nomeCampo = $filter('translate')('label-amostra');
                vm.detalharAmostra = false;

                vm.amostra = {};
                vm.amostra.dadosComplementares = [];
                vm.listaDadosAmostra = [];
                vm.listaDadosAmostrasSelecionadas = [];

                vm.showAvisoErroImportacao = false;

                var content_type = ['.xls'];

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_INCLUIR_EDITAR_DADO_MERCADO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                var init = function () {

                    pesquisarTiposAmostra();
                    buscarUsuarioLogado(function () {
                        if (avaliacaoEscopoCompartilhadoService.getObjeto('dadoUploadAmostra')) {
                            recuperarCompletarDadosAmotra();
                        }else if(avaliacaoEscopoCompartilhadoService.getObjeto('editarDadoUploadAmostra')){
                            buscarUploadAmostraEditar();
                        }
                    });
                };

                var buscarUploadAmostraEditar = function () {
                    vm.dadoUploadAmostra= avaliacaoEscopoCompartilhadoService.getObjeto('editarDadoUploadAmostra');
                    avaliacaoEscopoCompartilhadoService.limparEscopo();

                    amostraService.consultarDadosAmostraImportacao(vm.dadoUploadAmostra.id).then(function (res) {
                        vm.amostras = res.data;
                        vm.paginacaoDadosAmostra();
                    });
                };

                var recuperarCompletarDadosAmotra = function () {
                    vm.amostras = avaliacaoEscopoCompartilhadoService.getObjeto('amostras');
                    vm.dadoUploadAmostra = avaliacaoEscopoCompartilhadoService.getObjeto('dadoUploadAmostra');
                    avaliacaoEscopoCompartilhadoService.limparEscopo();

                    vm.paginacaoDadosAmostra();
                };

                vm.adicionarArquivoUpload = function (file) {
                    if (file) {
                        vm.file = file[0];
                    }
                };

                vm.limparArquivoUpload = function () {
                    if (vm.file) {
                        vm.file = null;
                    }
                    vm.showAvisoErroImportacao = false;
                };

                vm.uploadPlanilhaAmostra = function () {
                    if(vm.file && vm.file.name) {
                        if (vm.file.name.split(".")[vm.file.name.split(".").length - 1] === "xls") {
                            amostraService.uploadPlanilhaAmostra(vm.file).then(function (resp) {
                                vm.dadoUploadAmostra = resp.data
                                amostraService.importarPlanilhaAmostra(vm.dadoUploadAmostra.id).then(function (response) {
                                    vm.relatorioImportacao = new Blob([response.data], {type: 'text/plain'});
                                    if (vm.relatorioImportacao.size > 0) {
                                        vm.amostras = [];
                                        vm.paginacaoDadosAmostra();
                                        vm.showAvisoErroImportacao = true;
                                    } else {
                                        vm.showAvisoErroImportacao = false;
                                        amostraService.consultarDadosAmostraImportacao(vm.dadoUploadAmostra.id).then(function (res) {
                                            vm.amostras = res.data;
                                            vm.paginacaoDadosAmostra();
                                        });
                                    }
                                });
                            }, function () {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-incompatível'));
                            });
                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-incompatível'));
                        }
                    }
                };

                vm.downloadRetorioImportacao = function () {
                    var a = document.createElement("a");
                    document.body.appendChild(a);
                    a.style = "display: none";
                    var fileURL = URL.createObjectURL(vm.relatorioImportacao);
                    a.href = fileURL;
                    a.download = "relatorio_importacao.txt";
                    a.click();
                    window.URL.revokeObjectURL(fileURL);
                };

                var pesquisarTiposAmostra = function () {
                    vm.listaTiposAmostra = [];
                    amostraService.buscarTiposAmostra().then(function (resp) {
                        vm.listaTiposAmostraTab = resp.data;
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaTiposAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                    });
                };

                vm.pesquisarTiposDadosComplementares = function (tipo) {
                    amostraService.buscarTiposDadoComplementar(tipo).then(function (resp) {
                        vm.listaTiposDadosComplementares = resp.data;
                    });
                };

                vm.redirecionarParaConsultarUploadAmostra = function () {
                    $state.go('avaliacao.CONSULTAR_UPLOAD_AMOSTRA');
                };


                vm.completarDadosAmostra = function (id) {
                    var amostra = {};

                    for (var i = 0; i < vm.amostras.length; i++) {
                        if (vm.amostras[i].id === id) {
                            amostra = angular.copy(vm.amostras[i]);
                        }
                    }

                    avaliacaoEscopoCompartilhadoService.setObjetos('amostra', amostra);
                    avaliacaoEscopoCompartilhadoService.setObjetos('amostras', vm.amostras);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadoUploadAmostra', vm.dadoUploadAmostra);

                    $state.go('avaliacao.COMPLETAR_DADOS_AMOSTRA', {
                        idAmostra: id
                    });
                };


                vm.excluirAmostra = function ($index, idAmostra) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja remover este dado de mercado?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        amostraService.excluirAmostra(idAmostra).then(function () {
                            vm.amostras.splice($index, 1);
                            vm.paginacaoDadosAmostra();
                        });
                    }, function () {

                    });
                };

                vm.fecharUploadPlanilhaAmostra = function () {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Os dados importados não poderão ser recuperados. Deseja continuar?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        $state.go('avaliacao.CONSULTAR_UPLOAD_AMOSTRA');
                    }, function () {

                    });
                };

                vm.salvarRascunhoAmostra = function () {
                    amostraService.salvarRascunhoAmostraImportada(vm.amostras).then(function () {
                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-dados-salvos-sucesso'));
                        $state.go('avaliacao.CONSULTAR_UPLOAD_AMOSTRA');
                    });
                };

                vm.adicionarBaseDadosAmostra = function () {
                    for (var i = 0; i < vm.listaDadosAmostrasSelecionadas.length; i++) {
                        if (vm.listaDadosAmostrasSelecionadas.length === 0 || vm.listaDadosAmostrasSelecionadas[i].situacaoAmostra === 'IMPORTACAO' || vm.listaDadosAmostrasSelecionadas[i].situacaoAmostra === 'PENDENTE') {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-registros-pendencia-preenchimento'));
                            return;
                        }
                    }
                    amostraService.adicionarBaseDadosAmostra(vm.listaDadosAmostrasSelecionadas).then(function () {
                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-dados-mercado-adicionados-base'));
                        amostraService.consultarDadosAmostraImportacao(vm.dadoUploadAmostra.id).then(function (res) {
                            vm.amostras = res.data;
                            if (vm.amostras.length > 0) {
                                vm.paginacaoDadosAmostra();
                            } else {
                                $state.go('avaliacao.CONSULTAR_UPLOAD_AMOSTRA');
                            }
                        });

                    });
                };

                vm.showDialogOrientacoesPreenchimentoArquivo = function (ev) {
                    $mdDialog.show({
                        controller: function () {
                            return vm;
                        },
                        controllerAs: 'vm',
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogGerarPlanilhaAmostra.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: true
                    });
                };

                vm.cancelModal = function () {
                    $mdDialog.cancel();
                };

                vm.showExibirImagensImovel = function (ev, fotosImovel) {
                    $mdDialog.show({
                        controller: ["$scope", function ($scope) {
                            var vm = $scope;
                            vm.listaFotosImovel = fotosImovel;

                            vm.listaFotosImovel.forEach(function (fotoAmostra) {
                                fotoAmostra.imagem = fotoAmostra.arquivo.binario;
                            });

                            vm.fechar = function () {
                                $mdDialog.cancel();
                            };
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogFotosDetalharImovel.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true
                    });
                };

                vm.selecionarTodosItens = function () {
                    for (var i in vm.listaDadosAmostra) {
                        vm.listaDadosAmostra[i].isAmostraSelecionada = vm.isTodasAmostraSelecionada;
                    }
                    vm.selecionarItem();
                };

                vm.selecionarItem = function () {
                    vm.listaDadosAmostrasSelecionadas = ($filter('filter')(vm.listaDadosAmostra, {'isAmostraSelecionada': true}));
                    if (vm.listaDadosAmostrasSelecionadas.length === vm.listaDadosAmostra.length) {
                        vm.isTodasAmostraSelecionada = true;
                    } else if (vm.listaDadosAmostra.length > vm.listaDadosAmostrasSelecionadas.length || vm.listaDadosAmostrasSelecionadas.length === 0) {
                        vm.isTodasAmostraSelecionada = false;
                    }
                };

                vm.gerarImagem = function (base64) {
                    return 'data:image/png;base64,' + base64;
                };

                vm.paginacaoDadosAmostra = function () {
                    vm.totalItems = vm.amostras.length;

                    vm.listaDadosAmostra = [];
                    vm.totalItems = vm.amostras.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.listaDadosAmostra = vm.amostras;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.listaDadosAmostra.push(vm.amostras[i]);
                            if (vm.listaDadosAmostra.length === vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                init();
            }])
})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('selecionarAmostraController',
            ["$scope", "dominioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "$filter", "amostraService", "$state", "$mdMedia", "$mdDialog", "ComumFactory", "ciMapaAvaliacaoLayersFactory", "ciMapaAvaliacaoViewFactory", "NotificadorService", "MAPA_CONSTANTS", "$timeout", function ($scope, dominioAvaliacaoService, avaliacaoEscopoCompartilhadoService, $filter, amostraService,
                      $state, $mdMedia, $mdDialog, ComumFactory, ciMapaAvaliacaoLayersFactory, ciMapaAvaliacaoViewFactory, NotificadorService, MAPA_CONSTANTS, $timeout) {

                var vm = $scope;

                vm.amostra = {};
                vm.amostra.amostrasSelecionadas = [];
                vm.listaUFs = [];
                vm.listaMunicipios = [];
                vm.listaDadosAmostra = [];
                vm.listaDadosAmostrasSelecionadas = [];
                vm.amostra.apresentacao = 'TABELA';
                vm.isTodasAmostraSelecionada = false;
                ciMapaAvaliacaoLayersFactory.init();
                ciMapaAvaliacaoViewFactory.init();
                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;
                vm.carregarMapa = false;

                vm.condicoes = [];
                vm.condicoes.push(
                    {
                        nome: "Sim",
                        valor: true
                    },
                    {
                        nome: "Não",
                        valor: false
                    }
                )

                vm.listaSituacaoAmostra = {
                    ATIVO: "Ativo",
                    CANCELADO: "Cancelado"
                };

                var init = function () {
                    pesquisarTodasAmostras();
                    pesquisarUfs();
                    vm.pesquisarDadosAmostra();
                    pesquisarTiposAmostra();
                    pesquisarClassificacaoAmostra();
                    recuperarPropriedadesLaudoRelatorio();
                };

                vm.limparCamposPesquisa = function () {
                    vm.amostra = {};
                    vm.pesquisarDadosAmostra();
                };

                var pesquisarUfs = function () {
                    dominioAvaliacaoService.pesquisarUfsIntegrador().then(function (resp) {
                        vm.listaUFs = resp.data;
                    });
                };

                vm.pesquisarMunicipiosPorUf = function () {
                    if (vm.amostra.enderecamento.uf) {
                        dominioAvaliacaoService.pesquisarMuniciposPorUfIntegrador(vm.amostra.enderecamento.uf).then(function (resposta) {
                            vm.listaMunicipios = resposta.data;
                        });
                    }
                };

                var pesquisarTiposAmostra = function () {
                    vm.listaTiposAmostra = [];
                    amostraService.buscarTiposAmostra().then(function (resp) {
                        vm.listaTiposAmostraTab = resp.data;
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaTiposAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                    });
                };

                var pesquisarClassificacaoAmostra = function () {
                    amostraService.buscarClassificacaoAmostra().then(function (resp) {
                        vm.listaClassificacaoAmostra = [];
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaClassificacaoAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                    });
                };

                var pesquisarTodasAmostras = function () {
                    amostraService.allAmostras().then(function (resp) {
                        var amostras = resp.data;
                        NotificadorService.notify(vm, amostras, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_AMOSTRAS);
                    });
                };

                vm.pesquisarDadosAmostra = function () {
                    vm.amostra.situacaoAmostra = "ATIVO";
                    vm.order = 'codigoAmostra';
                    vm.orderBy = 'ASC';
                    amostraService.consultarDadosAmostra(vm.amostra, (vm.registroInicial - 1), vm.tamanhoLimite, vm.orderBy, vm.order)
                        .then(function (resp) {
                            vm.listaDadosAmostra = resp.data.content;
                            vm.totalItems = resp.data.totalElements;

                        });
                };

                vm.redirecionarTelaConsulta = function () {
                    voltarCadastrarLaudoRelatorio();
                };

                vm.editarAmostra = function (idAmostra) {
                    $state.go('avaliacao.EDITAR_AMOSTRA', {
                        idAmostra: idAmostra
                    });
                };

                vm.detalharAmostra = function (idAmostra) {
                    $state.go('avaliacao.DETALHAR_AMOSTRA', {
                        idAmostra: idAmostra
                    });
                };

                vm.cancelarAmostra = function (idAmostra) {
                    $state.go('avaliacao.CANCELAR_AMOSTRA', {
                        idAmostra: idAmostra
                    });
                };

                vm.gerarImagem = function (base64) {
                    return 'data:image/png;base64,' + base64;
                };

                vm.showExibirImagensImovel = function (ev, fotosImovel) {
                    $mdDialog.show({
                        controller: ["$scope", function ($scope) {
                            var vm = $scope;
                            vm.listaFotosImovel = fotosImovel;

                            vm.listaFotosImovel.forEach(function (fotoAmostra) {
                                fotoAmostra.imagem = fotoAmostra.arquivo.binario;
                            });

                            vm.fechar = function () {
                                $mdDialog.cancel();
                            };
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogFotosDetalharImovel.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true
                    });
                };

                vm.selecionarTodosItens = function () {
                    for (var i in vm.listaDadosAmostra) {
                        vm.listaDadosAmostra[i].isAmostraSelecionada = vm.isTodasAmostraSelecionada;
                    }
                    vm.selecionarItem();
                };

                vm.selecionarItem = function () {
                    vm.listaDadosAmostrasSelecionadas = ($filter('filter')(vm.listaDadosAmostra, {'isAmostraSelecionada': true}));
                    if (vm.listaDadosAmostrasSelecionadas.length === vm.listaDadosAmostra.length) {
                        vm.isTodasAmostraSelecionada = true;
                    } else if (vm.listaDadosAmostra.length > vm.listaDadosAmostrasSelecionadas.length || vm.listaDadosAmostrasSelecionadas.length === 0) {
                        vm.isTodasAmostraSelecionada = false;
                    }
                };

                vm.confimarAmostrasSelecionado = function () {
                    var ordem = 1;
                        if (vm.listaDadosAmostrasSelecionadas.length > 0) {
                        vm.avaliacao.amostras = angular.copy(vm.listaDadosAmostrasSelecionadas)
                        vm.avaliacao.amostras.forEach(function (amostra) {
                            amostra.isAmostraSelecionada = true;
                            amostra.avaliacao = {};
                            amostra.avaliacao.id = vm.avaliacao.id;
                            amostra.variavelAmostras = [];
                            vm.avaliacao.variaveis.forEach(function (variavel) {
                                var variavelAmostra = {};
                                variavelAmostra.variavel = {};
                                variavelAmostra.variavel.id = variavel.id;
                                variavelAmostra.amostra = {};
                                variavelAmostra.amostra.id = amostra.id;
                                variavelAmostra.ordem = ordem;
                                amostra.variavelAmostras.push(variavelAmostra);
                            });
                            ordem++;
                        });
                        amostraService.selecionarAmostra(vm.avaliacao.amostras).then(function () {
                            voltarCadastrarLaudoRelatorio();
                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-nenhum-dado-mercado-selecionada'));
                    }
                };

                vm.changeMapa = function (valor) {
                    if (valor === 'MAPA') {
                        vm.carregarMapa = true;
                        $timeout(function () {
                            marcarPontosMapa(vm.listaDadosAmostra);
                        }, 1000)
                    } else {
                        vm.carregarMapa = false;
                    }
                };

                var marcarPontosMapa = function (amostras) {
                    NotificadorService.notify(vm, amostras, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_AMOSTRAS);
                };

                var recuperarPropriedadesLaudoRelatorio = function () {
                    vm.laudoRelatorioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoRelatorioAvaliacao');
                    vm.dadosEnderecamento = avaliacaoEscopoCompartilhadoService.getObjeto('dadosEnderecamento');
                    vm.dadosProprietario = avaliacaoEscopoCompartilhadoService.getObjeto('dadosProprietario');
                    vm.benfeitoria = avaliacaoEscopoCompartilhadoService.getObjeto('benfeitoria');
                    vm.tipoTerreno = avaliacaoEscopoCompartilhadoService.getObjeto('tipoTerreno');

                    /**Identificacao**/
                    vm.ripImovel = avaliacaoEscopoCompartilhadoService.getObjeto('ripImovel');
                    vm.pontoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('pontoImovel');
                    vm.listaResponsaveisTecnicos = avaliacaoEscopoCompartilhadoService.getObjeto('listaResponsaveisTecnicos');
                    vm.listaNormasUtilizadas = avaliacaoEscopoCompartilhadoService.getObjeto('listaNormasUtilizadas');
                    /**Detalher Imovel**/
                    vm.listaMatriculasTranscricao = avaliacaoEscopoCompartilhadoService.getObjeto('listaMatriculasTranscricao');
                    vm.listaCertidoesCartoriais = avaliacaoEscopoCompartilhadoService.getObjeto('listaCertidoesCartoriais');
                    angular.forEach(vm.listaCertidoesCartoriais, function (certidaoCartorial) {
                        certidaoCartorial.arquivo.id = null;
                    });
                    vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                    vm.listaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosImovel');
                    /**Terreno Imovel**/
                    vm.terreno = avaliacaoEscopoCompartilhadoService.getObjeto('terreno');
                    vm.documentosParametrosUrbanisticos = avaliacaoEscopoCompartilhadoService.getObjeto('documentosParametrosUrbanisticos');
                    /**Benfeitorias**/
                    vm.listaBenfeitoriaAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriaAvaliacao');
                    vm.listaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosBenfeitorias');
                    /**Unidade Autonoma**/
                    vm.unidadeAutonomaIncorporacao = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaIncorporacao');
                    vm.listaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosUnidadeAutonoma');
                    /**Vistoria**/
                    vm.vistoriaListaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosImovel');
                    vm.vistoriaListaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosBenfeitorias');
                    vm.vistoriaListaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosUnidadeAutonoma');
                    /**Avaliacao**/
                    vm.tabSelect = avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect');
                    vm.avaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao');

                    vm.formularioIdentificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioIdentificacao');
                    vm.formularioDetalhesImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioDetalhesImovel');
                    vm.formularioTerrenoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioTerrenoImovel');
                    vm.formularioBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('formularioBenfeitorias');
                    vm.formularioUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUnidadeAutonoma');
                    vm.formularioUtilizacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUtilizacao');
                    vm.formularioVistoria = avaliacaoEscopoCompartilhadoService.getObjeto('formularioVistoria');
                    vm.formularioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAvaliacao');
                    vm.formularioResultadosRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosRelatorio');
                    vm.formularioResultadosLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosLaudo');
                    vm.formularioQuantificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacao');
                    vm.formularioQuantificacaoOrcamento = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacaoOrcamento');
                    vm.formularioResultadoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadoAvaliacao');
                    vm.formularioAnexo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAnexo');

                    avaliacaoEscopoCompartilhadoService.limparEscopo();
                };

                var voltarCadastrarLaudoRelatorio = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', vm.tipoTerreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', vm.benfeitoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);


                    $state.go('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: vm.laudoRelatorioAvaliacao.id
                    });
                };

                vm.changeSomenteArea = function () {
                    vm.amostra.areaTerreno = null;
                    vm.amostra.areaTerrenoFim = null;
                    vm.amostra.areaConstruida = null;
                    vm.amostra.areaConstruidaFim = null;
                };

                vm.changeSomenteValor = function () {
                    vm.amostra.valor = null;
                    vm.amostra.valorFim = null;
                    vm.amostra.valorLocacao = null;
                    vm.amostra.valorLocacaoFim = null;
                };

                init();
            }]);
}());
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('inserirAmostraController',
            ["$scope", "$rootScope", "laudoAvaliacaoService", "$stateParams", "dominioAvaliacaoService", "$state", "formularioAvaliacaoService", "$mdDialog", "usuarioAvaliacaoService", "identificacaoLaudoRelatorioService", "$filter", "CartografiaService", "amostraService", "MensagensService", "ComumFactory", "ciMapaAvaliacaoLayersFactory", "ciMapaAvaliacaoViewFactory", "MAPA_CONSTANTS", "NotificadorService", "imovelAvaliacaoService", "$timeout", "ectCepService", function ($scope, $rootScope, laudoAvaliacaoService, $stateParams, dominioAvaliacaoService, $state, formularioAvaliacaoService, $mdDialog, usuarioAvaliacaoService, identificacaoLaudoRelatorioService,
                      $filter, CartografiaService, amostraService, MensagensService, ComumFactory, ciMapaAvaliacaoLayersFactory, ciMapaAvaliacaoViewFactory, MAPA_CONSTANTS, NotificadorService, imovelAvaliacaoService, $timeout, ectCepService) {

                var vm = $scope;      
                var vmr = $rootScope;         

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;
                vm.nomeCampo = $filter('translate')('label-amostra');
                ciMapaAvaliacaoLayersFactory.init();
                ciMapaAvaliacaoViewFactory.init();
                var notifiers = MAPA_CONSTANTS.NOTIFICADORES;
                vm.detalharAmostra = false;
                vm.editarAmostra = false;
                vm.cepEncontrado = false;
                vm.desbloquearAmostra = false;
                vm.usuario = $rootScope.usuarioLogado || { permissoes: [], jurisdicoes: [] };

                vm.amostra = {};
                vm.amostra.arquivosComplementares = [];
                vm.amostra.dadosComplementares = [];

                vm.amostra.enderecamento = [];
                vm.amostra.enderecamento.geometria = [];
                vm.amostra.enderecamento.geometria = [];
                vm.bloquearMapa = true;

                var Dominio = {};
                Dominio.Constantes = {};
                Dominio.Constantes.Brasil = 'Brasil';

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_INCLUIR_EDITAR_DADO_MERCADO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                var init = function () {
                    buscarUsuarioLogado(function () {
                        loadMapas(function () {

                            pesquisarTodasAmostras();
                            pesquisarUfs();
                            pesquisarTiposLogradouro();
                            pesquisarTiposAmostra();
                            pesquisarClassificacaoAmostra();

                            if ($stateParams.idAmostra) {
                                amostraService.consultarAmostraId($stateParams.idAmostra).then(function (resp) {
                                    vm.amostra = resp.data;
                                    vm.changeValorAmotra(vm.amostra.valor);
                                    vm.changeValorAmotraLocacao(vm.amostra.valorLocacao);
                                    vm.amostra.tipoAmostraTemp = vm.amostra.tipoAmostra;
                                    NotificadorService.notify(vm, [vm.amostra.latitude, vm.amostra.longitude], MAPA_CONSTANTS.NOTIFICADORES.CENTRALIZAR_MAPA);
                                    pesquisarLaudosRelatorioVinculado();
                                    buscarTiposIdentificacao();
                                    if ($state.current.name.indexOf('DETALHAR_AMOSTRA') !== -1) {
                                        vm.detalharAmostra = true;
                                        vm.bloquearMapa = false;

                                        if (vm.amostra.dtCancelamento != null) {
                                            vm.amostra.dtCancelamento = $filter('date')(vm.amostra.dtCancelamento, 'dd/MM/yyyy');
                                        }
                                    } else {
                                        vm.editarAmostra = true;
                                        vm.bloquearMapa = false;
                                        if(!vm.amostra.tipoAmostra){
                                            vm.desbloquearAmostra = true;
                                        }
                                        if(!vm.amostra.enderecamento.uf){
                                            vm.pesquisarEnderecoPorCep();
                                        }
                                    }

                                    if (vm.amostra.dadosComplementares && vm.amostra.dadosComplementares.length > 0) {
                                        vm.amostra.isDadosComplementares = true;
                                    } else {
                                        vm.amostra.isDadosComplementares = [];
                                        vm.amostra.isDadosComplementares = false;
                                    }

                                    if (vm.amostra.arquivosComplementares && vm.amostra.arquivosComplementares.length > 0) {
                                        vm.amostra.isArquivosComplementares = true;
                                    } else {
                                        vm.amostra.isArquivosComplementares = [];
                                        vm.amostra.isArquivosComplementares = false;
                                    }

                                });
                            }
                        });
                        function loadMapas(callback) {

                            ciMapaAvaliacaoLayersFactory.init();
                            ciMapaAvaliacaoViewFactory.init();
                            if (callback) {
                                $timeout(function () {
                                    callback();
                                });
                            }
                        };
                    });
                };

                vm.salvarAmostra = function () {

                console.log('vm.formularioDadosAmostra');
                console.log(vm.formularioDadosAmostra);

                    vm.validarFormulario(vm.formularioDadosAmostra);
                    if (!vm.formularioDadosAmostra.$valid) {
                        return;
                    }
                    if (!vm.amostra.latitude || !vm.amostra.longitude) {
                        ComumFactory.mostrarMensagemError('Selecione um ponto no mapa!');
                        return
                    }

                    $timeout(function () {
                        amostraService.salvar(vm.amostra).then(function (resp) {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados-amostra') + resp.data.codigoAmostra);
                            $state.go('avaliacao.CONSULTAR_AMOSTRA');
                        });
                    });

                };

                vm.alterarAmostra = function () {
                    vm.validarFormulario(vm.formularioDadosAmostra);
                    if (!vm.formularioDadosAmostra.$valid) {
                        return;
                    }
                    if (!vm.amostra.latitude || !vm.amostra.longitude) {
                        ComumFactory.mostrarMensagemError('Selecione um ponto no mapa!');
                        return
                    }

                    $timeout(function () {
                        amostraService.salvar(vm.amostra).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-dados-alterados-sucesso'));
                            $state.go('avaliacao.CONSULTAR_AMOSTRA');
                        });
                    });

                };

                var pesquisarUfs = function () {
                    dominioAvaliacaoService.pesquisarUfsIntegrador().then(function (resp) {
                        vm.listaUFs = resp.data;
                    });
                };

                var pesquisarTodasAmostras = function () {
                    amostraService.allAmostras().then(function (resp) {
                        var amostras = resp.data;
                        for (var i = 0; i < amostras.length; i++) {
                            if (amostras[i].id === Number($stateParams.idAmostra)) {
                                amostras[i].pinAmarelo = true;
                                break;
                            }
                        }
                        NotificadorService.notify(vm, amostras, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_AMOSTRAS);
                    });
                };

                vm.pesquisarMunicipiosPorUf = function () {
                    if (vm.amostra.enderecamento.uf) {
                        dominioAvaliacaoService.pesquisarMuniciposPorUfIntegrador(vm.amostra.enderecamento.uf).then(function (resposta) {
                            vm.listaMunicipios = resposta.data;
                        });
                    }
                };

                var pesquisarTiposLogradouro = function () {
                    dominioAvaliacaoService.consultarTiposLogradouroSU().then(function (resp) {
                        vm.listaTiposLogradouro = resp.data;
                    });
                };

                var pesquisarTiposAmostra = function () {
                    vm.listaTiposAmostra = [];
                    amostraService.buscarTiposAmostra().then(function (resp) {
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaTiposAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                    });
                };

                var pesquisarClassificacaoAmostra = function () {
                    amostraService.buscarClassificacaoAmostra().then(function (resp) {
                        vm.listaClassificacaoAmostra = [];
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaClassificacaoAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                        vm.listaClassificacaoAmostra.push({
                            chave: null,
                            valor: null
                        });
                    });
                };

                vm.changeTipoAmostra = function () {
                    if(vm.amostra.dadosComplementares && vm.amostra.dadosComplementares.length > 0){
                        var confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent("Todos os dados complementares serão removidos. Deseja continuar?")
                            .ok("Confirmar")
                            .cancel("Cancelar");

                        $mdDialog.show(confirm).then(function () {
                            vm.amostra.isDadosComplementares = false;
                            vm.amostra.dadosComplementares = [];
                            vm.amostra.tipoAmostra = vm.amostra.tipoAmostraTemp;
                            vm.pesquisarTiposDadosComplementares(vm.amostra.tipoAmostra);
                        }, function () {
                            vm.amostra.tipoAmostraTemp = vm.amostra.tipoAmostra;
                        });
                    } else{
                        vm.amostra.tipoAmostra = vm.amostra.tipoAmostraTemp;
                        vm.pesquisarTiposDadosComplementares(vm.amostra.tipoAmostra);
                    }
                };

                vm.pesquisarTiposDadosComplementares = function (tipo) {
                    amostraService.buscarTiposDadoComplementar(tipo).then(function (resp) {
                        vm.listaTiposDadosComplementares = resp.data;
                    });
                };

                vm.changeClassificacaoAmostra = function (chave) {
                    if (chave === null) {
                        vm.amostra.classificacaoAmostra = undefined;
                    }
                };

                vm.changeClassificacaoAmostraLocacao = function (chave) {
                    if (chave === null) {
                        vm.amostra.classificacaoAmostraLocacao = undefined;
                    }
                };                

                vm.redirecionarParaConsultarAmostra = function () {
                    $state.go('avaliacao.CONSULTAR_AMOSTRA');
                };

                vm.redirecionarConsultarCep = function () {
                    window.open('http://www.buscacep.correios.com.br/sistemas/buscacep/', '_blank');
                };         
                
                vm.pesquisarEnderecoPorCep = function () {                    
                    var cep = vm.amostra.enderecamento.cep;
                    if (cep) {
                        amostraService.buscarEnderecoPorCep(cep).then(function (resp) {
                            vm.amostra.enderecamento = resp.data;
                            vm.pesquisarMunicipiosPorUf();
                            imovelAvaliacaoService.buscarGeometria(resp.data).then(function (result) {
                               var geometria = result.data && result.data !== "" ? result.data.coordinates[0][0] : vm.amostra.enderecamento.geometria.coordinates[0][0][0];
                                NotificadorService.notify(vm, [geometria[0], geometria[1]], MAPA_CONSTANTS.NOTIFICADORES.CENTRALIZAR_MAPA);
                            }); 

                            if(vm.amostra.enderecamento.cep.substring(5, 8) === '000'){
                                                                
                                vm.isCepGenerico = false;
                            }else{    
                                vm.isCepGenerico = true;
                                ectCepService.get({cep: cep}).$promise.then(successCallback, errorCallback);
                            }
                            vm.cepEncontrado = true;

                        }, function () {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-cep-invalido'));
                                vm.amostra.enderecamento = {};
                                vm.cepEncontrado = false;
                                vm.isCepGenerico = false;

                        });
                    } else {
                        vm.amostra.enderecamento = {};
                    }
                };                 

                vm.exibeMensagemPonto = function() {

                    if(ciMapaValidacaoFactory.validaCepPontoMunicipio(data.geometria, vmr.geomPonto.getGeometry().getCoordinates())){
                        console.log('inserir.amostra.controller - 293');
                        validaCep(data, false);
                    }else {
                        console.log('inserir.amostra.controller - 296');
                        MensagensService.exibirMensagemErro("O ponto do imóvel não se encontra sobre o município informado em relação ao CEP.");
                        vm.isCepGenerico = false;
                        vm.amostra.enderecamento.cep = "";
                    }

                    //MensagensService.exibirMensagemErro("O ponto do imóvel não se encontra sobre o município informado em relação ao CEP.");
                    //ciMapaValidacaoFactory.validaCepPontoMunicipio(data.geometria, vmr.geomPonto.getGeometry().getCoordinates())){
                };
                
                function successCallback(data) {
                    console.log('entrou sucesscallback');
                    console.log(vmr.geomPonto);
                    console.log(data.geometria);

                    vmr.geometriaMun = data.geometria;
                    if(vmr.geomPonto != undefined){
                        if(ciMapaValidacaoFactory.validaCepPontoMunicipio(data.geometria, vmr.geomPonto.getGeometry().getCoordinates())){
                            validaCep(data, false);                      
                            console.log('entrou success = 315');
                            //ciMapaAvaliacao.insere(data.feature);      
                        }else {
                            console.log('inserir.amostra.controller - 318');
                            MensagensService.exibirMensagemErro("O ponto do imóvel não se encontra sobre o município informado em relação ao CEP.");
                            vm.isCepGenerico = false;
                            vm.amostra.enderecamento.cep = "";
                        }
                    }else{
                        validaCep(data, false);
                    }

                }

                function validaCep(data, isPrimeiraConsulta){

                    if (data.uf !== vm.amostra.enderecamento.uf || data.municipio !== vm.amostra.enderecamento.municipio) {
                        vm.amostra.enderecamento = vm.imovelOriginal.enderecamento;
                        MensagensService.exibirMensagemErro("Este tipo de alteração não é permitido. Se ela for mesmo necessária, realize o cancelamento e inclua um novo imóvel.");
                    } else {
                        if (possuiPermissaoNaJurisdicao(data.uf)) {
                            data.pais = Dominio.Constantes.Brasil;
                            if(!isPrimeiraConsulta){
                                vm.amostra.enderecamento = data;
                            }
                            vm.isCepGenerico = data.stLocalidade == 0;
                            CartografiaService.buscarGeometria(vm.amostra.enderecamento).then(
                                function (result) {
                                    var geometria = result.data ? result.data : data.geometria;
                                    vmr.geometriaCep = result.data ? result.data : data.geometria;
                                    NotificadorService.notify($scope, geometria, notifiers.CENTRALIZAR_POR_MUNICIPIO);
                                }
                            );
                        } else {
                            vm.isCepGenerico = false;
                            vm.imovel.enderecamento = {};
                            vm.imovel.enderecamento.pais = Dominio.Constantes.Brasil;
                            MensagensService.exibirMensagemErro("Você não possui permissão para inserir imóveis nesta UF.");
                        }
                    }
                }

                function errorCallback(){
                    vm.isCepGenerico = false;
                    vm.imovel.enderecamento.cep = "";
                    MensagensService.exibirMensagemErro("CEP não encontrado");
                }

                vm.changeValorAmotra = function (valor) {
                    if (valor >= 1) {
                        vm.valorPorExtenso = escreverPorExtenso(valor.toString());
                    } else {
                        vm.valorPorExtenso = null;
                        vm.amostra.valor = null;
                    }
                };

                vm.changeValorAmotraLocacao = function (valor) {
                    if (valor >= 1) {
                        vm.valorLocacaoPorExtenso = escreverPorExtenso(valor.toString());
                    } else {
                        vm.valorLocacaoPorExtenso = null;
                        vm.amostra.valorLocacao = null;
                    }
                };

                vm.changeAreaTerreno = function (valor) {
                    if (valor == 0) {
                        vm.amostra.areaTerreno = null;
                    }
                };

                vm.changeAreaConstruida = function (valor) {
                    if (valor == 0) {
                        vm.amostra.areaConstruida = null;
                    }
                };

                vm.limparDadosComplementares = function (aux) {
                    vm.amostra.isDadosComplementares = true;
                    if (!aux) {
                        if (vm.amostra.dadosComplementares.length > 0) {
                            var confirm = $mdDialog.confirm()
                                .title("Atenção!")
                                .textContent("Todos os dados complementares serão removidos. Deseja continuar?")
                                .ok("Confirmar")
                                .cancel("Cancelar");

                            $mdDialog.show(confirm).then(function () {
                                vm.amostra.dadosComplementares = [];
                                vm.amostra.isDadosComplementares = false;
                            }, function () {

                            });
                        }else{
                            vm.amostra.isDadosComplementares = false;
                        }
                    }
                };

                function possuiPermissaoNaJurisdicao(uf) {
                    return vm.usuario.jurisdicoes.includes(uf);
                }

                vm.limparArquivosComplementares = function (aux) {
                    vm.amostra.isArquivosComplementares = true;
                    if (!aux) {
                        if (vm.amostra.arquivosComplementares.length > 0) {
                            var confirm = $mdDialog.confirm()
                                .title("Atenção!")
                                .textContent("Todos os arquivos complementares serão removidos. Deseja continuar?")
                                .ok("Confirmar")
                                .cancel("Cancelar");

                            $mdDialog.show(confirm).then(function () {
                                vm.amostra.arquivosComplementares = [];
                                vm.amostra.isArquivosComplementares = false;
                            }, function () {

                            });
                        }else{
                            vm.amostra.isArquivosComplementares = false;
                        }
                    }
                };

                vm.limparFotos = function (aux) {
                    vm.amostra.isFotos = true;
                    if (!aux) {
                        if (vm.amostra.fotos.length > 0) {
                            var confirm = $mdDialog.confirm()
                                .title("Atenção!")
                                .textContent("Todas as fotos serão removidas. Deseja continuar?")
                                .ok("Confirmar")
                                .cancel("Cancelar");

                            $mdDialog.show(confirm).then(function () {
                                vm.amostra.fotos = [];
                                vm.amostra.isFotos = false;
                            }, function () {

                            });
                        }else{
                            vm.amostra.isFotos = false;
                        }
                    }
                };

                var escreverPorExtenso = function (c) {
                    var ex = [
                        ["zero", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove", "dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"],
                        ["dez", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"],
                        ["cem", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"],
                        ["mil", "milhão", "bilhão", "trilhão", "quadrilhão", "quintilhão", "sextilhão", "setilhão", "octilhão", "nonilhã", "decilhão", "undecilhão", "dodecilhão", "tredecilhão", "quatrodecilhão", "quindecilhão", "sedecilhão", "septendecilhão", "octencilhão", "nonencilhão"]
                    ];
                    var a, n, v, i, n = c.replace(c ? /[^,\d]/g : /\D/g, "").split(","), e = " e ", $ = "real",
                        d = "centavo", sl;
                    for (var f = n.length - 1, l, j = -1, r = [], s = [], t = ""; ++j <= f; s = []) {
                        j && (n[j] = (("." + n[j]) * 1).toFixed(2).slice(2));
                        if (!(a = (v = n[j]).slice((l = v.length) % 3).match(/\d{3}/g), v = l % 3 ? [v.slice(0, l % 3)] : [], v = a ? v.concat(a) : v).length) continue;
                        for (a = -1, l = v.length; ++a < l; t = "") {
                            if (!(i = v[a] * 1)) continue;
                            i % 100 < 20 && (t += ex[0][i % 100]) ||
                            i % 100 + 1 && (t += ex[1][(i % 100 / 10 >> 0) - 1] + (i % 10 ? e + ex[0][i % 10] : ""));
                            s.push((i < 100 ? t : !(i % 100) ? ex[2][i == 100 ? 0 : i / 100 >> 0] : (ex[2][i / 100 >> 0] + e + t)) +
                                ((t = l - a - 2) > -1 ? " " + (i > 1 && t > 0 ? ex[3][t].replace("?o", "?es") : ex[3][t]) : ""));
                        }
                        a = ((sl = s.length) > 1 ? (a = s.pop(), s.join(" ") + e + a) : s.join("") || ((!j && (n[j + 1] * 1 > 0) || r.length) ? "" : ex[0][0]));
                        a && r.push(a + (c ? (" " + (v.join("") * 1 > 1 ? j ? d + "s" : (/0{6,}$/.test(n[0]) ? "de " : "") + $.replace("l", "is") : j ? d : $)) : ""));
                    }
                    return r.join(e);
                };
                var desenhaBenfeitoriasMapa = function (benfeitorias) {
                    NotificadorService.notify(vm, benfeitorias, MAPA_CONSTANTS.NOTIFICADORES.DESENHAR_BENFEITORIAS);
                };

                vm.apresentaImoveisUniao = function (mostrar) {
                    if (mostrar && (vm.cepEncontrado || (vm.amostra.enderecamento && vm.amostra.enderecamento.municipio ))) {
                        amostraService.benfeitoriaUniao(vm.amostra.enderecamento.municipio).then(function (resp) {
                            vm.benfeitorias = resp.data;
                            desenhaBenfeitoriasMapa(vm.benfeitorias);
                        });
                    } else {
                        NotificadorService.notify(vm, '', MAPA_CONSTANTS.NOTIFICADORES.LIMPAR_BENFEITORIAS);
                        if (vm.mostrarUniao) {
                            vm.mostrarUniao = false;
                        }
                    }

                };

                vm.validarValorLaudoRelatorio = function (tipoValor, resultadoAvaliacao) {
                    if(tipoValor){
                        if (tipoValor === 'VALOR_MEDIO') {
                            if(resultadoAvaliacao.valorMedio){
                                return resultadoAvaliacao.moeda.simbolo + ' ' + $filter('currency')(resultadoAvaliacao.valorMedio).replace('R$', '');
                            }

                        } else if (tipoValor === 'VALOR_MINIMO') {
                            if(resultadoAvaliacao.valorMinimo){
                                return resultadoAvaliacao.moeda.simbolo + ' ' + $filter('currency')(resultadoAvaliacao.valorMinimo).replace('R$', '');
                            }

                        } else if (tipoValor === 'VALOR_MAXIMO') {
                            if(resultadoAvaliacao.valorMaximo){
                                return resultadoAvaliacao.moeda.simbolo + ' ' + $filter('currency')(resultadoAvaliacao.valorMaximo).replace('R$', '');
                            }

                        } else if (tipoValor === 'OUTRO_VALOR') {
                            if(resultadoAvaliacao.outroValorAdotado){
                                return resultadoAvaliacao.moeda.simbolo + ' ' + $filter('currency')(resultadoAvaliacao.outroValorAdotado).replace('R$', '');
                            }
                        }
                    }
                };

                var pesquisarLaudosRelatorioVinculado = function () {
                    amostraService.buscarLaudosRelatorioVinculado(vm.amostra.id).then(function (resp) {
                        vm.paginationDadosUtilizacao = resp.data;
                    });
                };

                var buscarTiposIdentificacao = function () {
                    identificacaoLaudoRelatorioService.buscarTiposIdentificacao().then(function (resp) {
                        vm.tiposIdentificacao = resp.data;
                    });
                };

                vm.ripImovelLink = function (rip) {
                    window.open('#/incorporacao/detalharImovel/' + rip, '_blank');
                };

                vm.redirecionarDetalharLaudoAvaliacao = function (id) {
                    window.open('#/avaliacao/detalharLaudoRelatorioAvaliacao/' + id, '_blank');
                };

                init();
            }])
})();

(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('gerarPlanilhaAmostraController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "dominioAvaliacaoService", "$state", "formularioAvaliacaoService", "$mdDialog", "usuarioAvaliacaoService", "$filter", "amostraService", "ComumFactory", "$timeout", function ($scope, laudoAvaliacaoService, $stateParams, dominioAvaliacaoService, $state, formularioAvaliacaoService, $mdDialog, usuarioAvaliacaoService,
                      $filter, amostraService, ComumFactory, $timeout) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;
                vm.nomeCampo = $filter('translate')('label-amostra');
                vm.detalharAmostra = false;

                vm.amostra = {};
                vm.amostra.dadosComplementares = [];

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_INCLUIR_EDITAR_DADO_MERCADO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                var init = function () {
                    buscarUsuarioLogado(function () {
                        pesquisarTiposAmostra();
                    });
                };

                vm.gerarPlanilha = function () {
                    vm.validarFormulario(vm.formularioDadosAmostra);
                    if (!vm.formularioDadosAmostra.$valid) {
                        return;
                    }

                    var a = document.createElement("a");
                    document.body.appendChild(a);
                    a.style = "display: none";

                    amostraService.gerarPlanilhaAmostra(vm.amostra).then( function(response) {
                        var file =new Blob([response.data], {type: 'application/vnd.ms-excel'});
                        var fileURL = URL.createObjectURL(file);
                        a.href = fileURL;
                        a.download = "dados_mercado.xls";
                        a.click();
                        window.URL.revokeObjectURL(fileURL);
                    });
                };

                var pesquisarTiposAmostra = function () {
                    vm.listaTiposAmostra = [];
                    amostraService.buscarTiposAmostra().then(function (resp) {
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaTiposAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                    });
                };


                vm.changeTipoAmostra = function () {
                    if (vm.amostra.dadosComplementares && vm.amostra.dadosComplementares.length > 0) {
                        var confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent("Todos os dados complementares serão removidos. Deseja continuar?")
                            .ok("Confirmar")
                            .cancel("Cancelar");

                        $mdDialog.show(confirm).then(function () {
                            vm.amostra.isDadosComplementares = false;
                            vm.amostra.dadosComplementares = [];
                            vm.amostra.tipoAmostra = vm.amostra.tipoAmostraTemp;
                            vm.pesquisarTiposDadosComplementares(vm.amostra.tipoAmostra);
                        }, function () {
                            vm.amostra.tipoAmostraTemp = vm.amostra.tipoAmostra;
                        });
                    } else {
                        vm.amostra.tipoAmostra = vm.amostra.tipoAmostraTemp;
                        vm.pesquisarTiposDadosComplementares(vm.amostra.tipoAmostra);
                    }
                };

                vm.pesquisarTiposDadosComplementares = function (tipo) {
                    amostraService.buscarTiposDadoComplementar(tipo).then(function (resp) {
                        vm.listaTiposDadosComplementares = resp.data;
                    });
                };

                vm.redirecionarParaConsultarAmostra = function () {
                    $state.go('avaliacao.CONSULTAR_AMOSTRA');
                };

                vm.limparDadosComplementares = function (aux) {
                    vm.amostra.isDadosComplementares = true;
                    if (!aux) {
                        if (vm.amostra.dadosComplementares.length > 0) {
                            var confirm = $mdDialog.confirm()
                                .title("Atenção!")
                                .textContent("Todos os dados complementares serão removidos. Deseja continuar?")
                                .ok("Confirmar")
                                .cancel("Cancelar");

                            $mdDialog.show(confirm).then(function () {
                                vm.amostra.dadosComplementares = [];
                                vm.amostra.isDadosComplementares = false;
                            }, function () {

                            });
                        } else {
                            vm.amostra.isDadosComplementares = false;
                        }
                    }
                };

                vm.showDialogOrientacoesPreenchimentoArquivo = function (ev) {
                    $mdDialog.show({
                        controller: function () {
                            return vm;
                        },
                        controllerAs: 'vm',
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogGerarPlanilhaAmostra.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: true
                    });
                };

                vm.cancelModal = function () {
                    $mdDialog.cancel();
                };

                init();
            }])
})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('consultarUploadAmostraController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "arquivoAvaliacaoService", "$state", "formularioAvaliacaoService", "$mdDialog", "usuarioAvaliacaoService", "$filter", "amostraService", "ComumFactory", "avaliacaoEscopoCompartilhadoService", function ($scope, laudoAvaliacaoService, $stateParams, arquivoAvaliacaoService, $state, formularioAvaliacaoService, $mdDialog, usuarioAvaliacaoService,
                      $filter, amostraService, ComumFactory, avaliacaoEscopoCompartilhadoService) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.filtro = {};

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_INCLUIR_EDITAR_DADO_MERCADO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                var init = function () {
                    buscarUsuarioLogado(function () {
                        vm.pesquisarDadosUploadAmostra();
                    });
                };


                vm.limparFiltro = function () {
                   vm.filtro = {};
                    vm.pesquisarDadosUploadAmostra();
                };

                vm.redirecionarUploadPlanilhaAmostra = function () {

                    $state.go('avaliacao.UPLOAD_PLANILHA_AMOSTRA');
                };

                vm.editarDadoUploadAmostra = function (dadoUploadAmostra) {
                    avaliacaoEscopoCompartilhadoService.setObjetos('editarDadoUploadAmostra', dadoUploadAmostra);
                    $state.go('avaliacao.UPLOAD_PLANILHA_AMOSTRA');
                };

                vm.pesquisarDadosUploadAmostra = function () {
                    vm.orderBy = 'ASC';
                    vm.order = 'dtUpload';

                    amostraService.consultarDadosUploadAmostra(vm.filtro, (vm.registroInicial - 1), vm.tamanhoLimite, vm.orderBy, vm.order)
                        .then(function (resp) {
                            vm.dadosUploadAmostra = resp.data.content;
                            vm.totalItems = resp.data.totalElements;
                        });
                };

                vm.excluirDadoUploadAmostra = function (idDadoUploadAmostra) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Todos os Dados de Mercado serão excluídos. Deseja confirmar a exclusão?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        amostraService.excluirDadoUploadAmostra(idDadoUploadAmostra).then(function () {
                            vm.pesquisarDadosUploadAmostra();
                        });
                    }, function () {

                    });
                };

                init();
            }])
})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('consultarAmostraController',
            ["$scope", "dominioAvaliacaoService", "$filter", "amostraService", "$timeout", "usuarioAvaliacaoService", "$state", "$mdMedia", "$mdDialog", "ciMapaAvaliacaoLayersFactory", "ciMapaAvaliacaoViewFactory", "NotificadorService", "MAPA_CONSTANTS", function ($scope, dominioAvaliacaoService, $filter, amostraService, $timeout, usuarioAvaliacaoService,
                      $state, $mdMedia, $mdDialog, ciMapaAvaliacaoLayersFactory, ciMapaAvaliacaoViewFactory, NotificadorService, MAPA_CONSTANTS) {

                var vm = $scope;

                vm.amostra = {};
                vm.listaUFs = [];
                vm.listaMunicipios = [];
                vm.listaDadosAmostra = [];
                vm.listaDadosAmostrasSelecionadas = [];
                vm.amostra.apresentacao = 'TABELA';
                ciMapaAvaliacaoLayersFactory.init();
                ciMapaAvaliacaoViewFactory.init();
                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;
                vm.carregarMapa = false;
                vm.carregarMapaModal = false;
                vm.condicoes = [];
                vm.condicoes.push(
                    {
                        nome: "Sim",
                        valor: true
                    },
                    {
                        nome: "Não",
                        valor: false
                    }
                )

                vm.listaSituacaoAmostra = {
                    ATIVO: "Ativo",
                    CANCELADO: "Cancelado"
                };

                var init = function () {
                    buscarUsuarioLogado(function () {
                        pesquisarUfs();
                        vm.pesquisarDadosAmostra();
                        pesquisarTiposAmostra();
                        pesquisarClassificacaoAmostra();
                    });
                };

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_CONSULTAR_VISUALIZAR_DADO_MERCADO === 'false' && vm.usuarioLogado.AVALIACAO_INCLUIR_EDITAR_DADO_MERCADO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });

                };

                vm.limparCamposPesquisa = function () {
                    vm.amostra = {};
                    vm.pesquisarDadosAmostra();
                };

                var pesquisarUfs = function () {
                    dominioAvaliacaoService.pesquisarUfsIntegrador().then(function (resp) {
                        vm.listaUFs = resp.data;
                    });
                };

                vm.pesquisarMunicipiosPorUf = function () {
                    if (vm.amostra.enderecamento.uf) {
                        dominioAvaliacaoService.pesquisarMuniciposPorUfIntegrador(vm.amostra.enderecamento.uf).then(function (resposta) {
                            vm.listaMunicipios = resposta.data;
                        });
                    }
                };

                var pesquisarTiposAmostra = function () {
                    vm.listaTiposAmostra = [];
                    amostraService.buscarTiposAmostra().then(function (resp) {
                        vm.listaTiposAmostraTab = resp.data;
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaTiposAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                    });
                };

                var pesquisarClassificacaoAmostra = function () {
                    amostraService.buscarClassificacaoAmostra().then(function (resp) {
                        vm.listaClassificacaoAmostra = [];
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaClassificacaoAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                    });
                };

                vm.pesquisarDadosAmostra = function (valor) {
                    vm.carregarMapa = false;
                    if (vm.orderBy === 'ASC') {
                        vm.orderBy = 'DESC';
                    } else {
                        vm.orderBy = 'ASC';
                    }
                    if (vm.order) {
                        vm.order = vm.order.replace('-', '');
                    } else {
                        vm.order = 'codigoAmostra';
                    }


                    amostraService.consultarDadosAmostra(vm.amostra, (vm.registroInicial - 1), vm.tamanhoLimite, vm.orderBy, vm.order)
                        .then(function (resp) {
                            vm.listaDadosAmostra = resp.data.content;
                            vm.totalItems = resp.data.totalElements;
                            // marcarPontosMapa(vm.listaDadosAmostra);
                            amostraService.benfeitoriaUniao().then(function (resp) {
                                vm.benfeitorias = resp.data;
                            });
                            if (valor === 'MAPA') {
                                vm.carregarMapa = true;
                                $timeout(function () {
                                    vm.amostra.situacaoAmostra = "ATIVO";
                                    marcarPontosMapa(vm.listaDadosAmostra);
                                }, 1000)
                            }
                        });
                    if (vm.orderBy === 'DESC') {
                        vm.order = '-' + vm.order;
                    }

                };

                vm.incluirAmostra = function () {
                    $state.go('avaliacao.INSERIR_AMOSTRA');
                };

                vm.editarAmostra = function (idAmostra) {
                    $state.go('avaliacao.EDITAR_AMOSTRA', {
                        idAmostra: idAmostra
                    });
                };

                vm.detalharAmostra = function (idAmostra) {
                    $state.go('avaliacao.DETALHAR_AMOSTRA', {
                        idAmostra: idAmostra
                    });
                };

                vm.cancelarAmostra = function (idAmostra) {
                    $state.go('avaliacao.CANCELAR_AMOSTRA', {
                        idAmostra: idAmostra
                    });
                };

                vm.gerarImagem = function (base64) {
                    return 'data:image/png;base64,' + base64;
                };


                vm.showExibirImagensImovel = function (ev, fotosImovel) {
                    $mdDialog.show({
                        controller: ["$scope", function ($scope) {
                            var vm = $scope;
                            vm.listaFotosImovel = fotosImovel;

                            vm.listaFotosImovel.forEach(function (fotoAmostra) {
                                fotoAmostra.imagem = fotoAmostra.arquivo.binario;
                            });

                            vm.fechar = function () {
                                $mdDialog.cancel();
                            };
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogFotosDetalharImovel.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true
                    });
                };

                vm.changeMapa = function (valor) {
                    if (vm.orderBy === 'ASC') {
                        vm.orderBy = 'DESC';
                    } else {
                        vm.orderBy = 'ASC';
                    }
                    if (vm.order) {
                        vm.order = vm.order.replace('-', '');
                    } else {
                        vm.order = 'codigoAmostra';
                    }

                    if (valor === 'MAPA') {
                        vm.carregarMapa = true;
                        $timeout(function () {
                            vm.amostra.situacaoAmostra = "ATIVO";
                            amostraService.consultarDadosAmostra(vm.amostra, (vm.registroInicial - 1), vm.tamanhoLimite, vm.orderBy, vm.order)
                                .then(function (resp) {
                                    vm.listaDadosAmostra = resp.data.content;
                                    vm.totalItems = resp.data.totalElements;
                                    marcarPontosMapa(vm.listaDadosAmostra);
                                    amostraService.benfeitoriaUniao().then(function (resp) {
                                        vm.benfeitorias = resp.data;
                                    });
                                });

                        }, 1000)
                    } else {
                        vm.carregarMapa = false;
                        vm.amostra.situacaoAmostra = null;
                        amostraService.consultarDadosAmostra(vm.amostra, (vm.registroInicial - 1), vm.tamanhoLimite, vm.orderBy, vm.order)
                            .then(function (resp) {
                                vm.listaDadosAmostra = resp.data.content;
                                vm.totalItems = resp.data.totalElements;
                                amostraService.benfeitoriaUniao().then(function (resp) {
                                    vm.benfeitorias = resp.data;
                                });
                            });
                    }
                };

                var marcarPontosMapa = function (amostras) {
                    NotificadorService.notify(vm, amostras, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_AMOSTRAS);
                };

                var desenhaBenfeitoriasMapa = function (benfeitorias) {
                    NotificadorService.notify(vm, benfeitorias, MAPA_CONSTANTS.NOTIFICADORES.DESENHAR_BENFEITORIAS);
                };

                vm.selecionarTodosItens = function () {
                    for (var i in vm.listaDadosAmostra) {
                        vm.listaDadosAmostra[i].isAmostraSelecionada = vm.isTodasAmostraSelecionada;
                    }
                    vm.selecionarItem();
                };

                vm.selecionarItem = function () {
                    vm.listaDadosAmostrasSelecionadas = ($filter('filter')(vm.listaDadosAmostra, {'isAmostraSelecionada': true}));
                    if (vm.listaDadosAmostrasSelecionadas.length === vm.listaDadosAmostra.length) {
                        vm.isTodasAmostraSelecionada = true;
                    } else if (vm.listaDadosAmostra.length > vm.listaDadosAmostrasSelecionadas.length || vm.listaDadosAmostrasSelecionadas.length === 0) {
                        vm.isTodasAmostraSelecionada = false;
                    }
                };

                vm.gerarPlanilhaAmostras = function () {
                    if (vm.listaDadosAmostrasSelecionadas.length > 0) {
                        var listaIds = "";
                        for (var i = 0; i < vm.listaDadosAmostrasSelecionadas.length; i++) {
                            if (i !== 0) {
                                listaIds = listaIds + ",";
                            }
                            listaIds = listaIds + vm.listaDadosAmostrasSelecionadas[i].id;

                        }
                        var a = document.createElement("a");
                        a.setAttribute("href", '/avaliacao/api/amostra/exportar-amostras/' + listaIds);
                        a.setAttribute("target", "_blank");
                        var clickEvent = new MouseEvent("click", {
                            "view": window,
                            "bubbles": true,
                            "cancelable": false
                        });
                        a.dispatchEvent(clickEvent);
                    }
                };


                vm.abrirDialogMapaAmostra = function (ev, amostra) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", "ciMapaAvaliacaoLayersFactory", "ciMapaAvaliacaoViewFactory", "NotificadorService", "MAPA_CONSTANTS", function ($scope, ciMapaAvaliacaoLayersFactory, ciMapaAvaliacaoViewFactory, NotificadorService, MAPA_CONSTANTS) {
                            var vm = $scope;

                            loadMapas(function () {

                                vm.amostrasDialog = [];
                                vm.amostrasDialog.push(amostra);

                                vm.carregarMapaModal = true;

                                $timeout(function () {
                                    marcarPontosMapa(vm.amostrasDialog);
                                }, 1000);

                            });

                            function loadMapas(callback) {

                                ciMapaAvaliacaoLayersFactory.init();
                                ciMapaAvaliacaoViewFactory.init();
                                if (callback) {
                                    $timeout(function () {
                                        callback();
                                    });
                                }
                            };

                            var marcarPontosMapa = function (amostras) {
                                NotificadorService.notify(vm, amostras, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_AMOSTRAS);
                            };

                            vm.fecharDialogMapaAmostra = function () {
                                vm.carregarMapaModal = false;
                                $mdDialog.cancel();
                            };
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogMapaAmostra.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                init();
            }]);
}());
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('completarDadosAmostraController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "dominioAvaliacaoService", "$state", "formularioAvaliacaoService", "$mdDialog", "usuarioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "$filter", "amostraService", "ComumFactory", "ciMapaAvaliacaoLayersFactory", "ciMapaAvaliacaoViewFactory", "MAPA_CONSTANTS", "NotificadorService", "imovelAvaliacaoService", "$timeout", "identificacaoLaudoRelatorioService", function ($scope, laudoAvaliacaoService, $stateParams, dominioAvaliacaoService, $state, formularioAvaliacaoService, $mdDialog, usuarioAvaliacaoService, avaliacaoEscopoCompartilhadoService,
                      $filter, amostraService, ComumFactory, ciMapaAvaliacaoLayersFactory, ciMapaAvaliacaoViewFactory, MAPA_CONSTANTS, NotificadorService, imovelAvaliacaoService, $timeout, identificacaoLaudoRelatorioService) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;
                vm.nomeCampo = $filter('translate')('label-amostra');
                ciMapaAvaliacaoLayersFactory.init();
                ciMapaAvaliacaoViewFactory.init();
                vm.detalharAmostra = false;
                vm.editarAmostra = false;
                vm.cepEncontrado = false;
                vm.bloquearMapa = true;
                vm.desbloquearAmostra = false;

                vm.amostra = {};
                vm.listaCondicoes = {SIM: true, NAO: false};
                vm.listaNomesVariavelCompleta = {
                    AREA_TERRENO: "Área do terreno",
                    AREA_CONSTRUIDA: "Área Construída",
                    PADRAO_ACABAMENTO: "Padrão de Acabamento",
                    ESTADO_CONSERVACAO: "Estado e Conservação",
                    ELEVADOR: "Elevador",
                    GARAGEM: "Garagem",
                    ESQUINA: "Esquina",
                    POTENCIAL_CONSTRUTIVO: "Potencial Construtivo",
                    TESTADA: "Testada",
                    TOPOGRAFIA: "Topografia",
                    VALOR_TOTAL: "Valor Total",
                    VALOR_UNITARIO: "Valor Unitário",
                    VALOR_TOTAL_LOCACAO: "Valor Total de Locação",
                    VALOR_UNITARIO_LOCACAO: "Valor Unitário de Locação",
                    OUTRO: "Outro"
                };
                vm.amostra.arquivosComplementares = [];
                vm.amostra.dadosComplementares = [];

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_INCLUIR_EDITAR_DADO_MERCADO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                var init = function () {
                    buscarUsuarioLogado(function () {
                        loadMapas(function () {

                            pesquisarTodasAmostras();
                            pesquisarUfs();
                            pesquisarTiposLogradouro();
                            pesquisarTiposAmostra();
                            pesquisarClassificacaoAmostra();
                            recuperarUploadPlanilhaAmostra(function () {
                                vm.changeValorAmotra(vm.amostra.valor);
                                vm.changeValorAmotraLocacao(vm.amostra.valorLocacao);
                                NotificadorService.notify(vm, [vm.amostra.latitude, vm.amostra.longitude], MAPA_CONSTANTS.NOTIFICADORES.CENTRALIZAR_MAPA);
                                vm.editarAmostra = true;
                                vm.bloquearMapa = false;
                                if (!vm.amostra.tipoAmostra) {
                                    vm.desbloquearAmostra = true;
                                }
                                if (!vm.amostra.enderecamento.uf) {
                                    vm.pesquisarEnderecoPorCep();
                                }

                                if (vm.amostra.dadosComplementares && vm.amostra.dadosComplementares.length > 0) {
                                    vm.amostra.isDadosComplementares = true;
                                } else {
                                    vm.amostra.isDadosComplementares = [];
                                    vm.amostra.isDadosComplementares = false;
                                }

                                if (vm.amostra.arquivosComplementares && vm.amostra.arquivosComplementares.length > 0) {
                                    vm.amostra.isArquivosComplementares = true;
                                } else {
                                    vm.amostra.isArquivosComplementares = [];
                                    vm.amostra.isArquivosComplementares = false;
                                }
                            });

                        });

                        function loadMapas(callback) {
                            ciMapaAvaliacaoLayersFactory.init();
                            ciMapaAvaliacaoViewFactory.init();
                            if (callback) {
                                $timeout(function () {
                                    callback();
                                });
                            }
                        };
                    });
                };

                vm.salvarAmostra = function () {
                    vm.validarFormulario(vm.formularioDadosAmostra);
                    if (!vm.formularioDadosAmostra.$valid) {
                        ComumFactory.mostrarMensagemError('Necessário preencher os campos obrigatórios');
                        return;
                    }
                    if (!vm.amostra.latitude || !vm.amostra.longitude) {
                        ComumFactory.mostrarMensagemError('Necessário preencher os campos obrigatórios');
                        return
                    }

                   for(var i =0; i < vm.amostras.length; i++) {
                        if(vm.amostras[i].id === vm.amostra.id){
                            vm.amostra.situacaoAmostra = 'COMPLETADO';
                           vm.amostras[i] = vm.amostra;
                           break;
                        }
                    }
                    ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                    voltarUploadPlanilhaAmostra();

                };

                var pesquisarUfs = function () {
                    dominioAvaliacaoService.pesquisarUfsIntegrador().then(function (resp) {
                        vm.listaUFs = resp.data;
                    });
                };

                var pesquisarTodasAmostras = function () {
                    amostraService.allAmostras().then(function (resp) {
                        var amostras = resp.data;
                        for (var i = 0; i < amostras.length; i++) {
                            if (amostras[i].id === Number($stateParams.idAmostra)) {
                                amostras[i].pinAmarelo = true;
                                break;
                            }
                        }
                        NotificadorService.notify(vm, amostras, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_AMOSTRAS);
                    });
                };

                vm.pesquisarMunicipiosPorUf = function () {
                    if (vm.amostra.enderecamento.uf) {
                        dominioAvaliacaoService.pesquisarMuniciposPorUfIntegrador(vm.amostra.enderecamento.uf).then(function (resposta) {
                            vm.listaMunicipios = resposta.data;
                        });
                    }
                };

                var pesquisarTiposLogradouro = function () {
                    dominioAvaliacaoService.consultarTiposLogradouroSU().then(function (resp) {
                        vm.listaTiposLogradouro = resp.data;
                    });
                };

                var pesquisarTiposAmostra = function () {
                    vm.listaTiposAmostra = [];
                    amostraService.buscarTiposAmostra().then(function (resp) {
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaTiposAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                    });
                };

                var pesquisarClassificacaoAmostra = function () {
                    amostraService.buscarClassificacaoAmostra().then(function (resp) {
                        vm.listaClassificacaoAmostra = [];
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaClassificacaoAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                        vm.listaClassificacaoAmostra.push({
                            chave: null,
                            valor: null
                        });
                    });
                };

                vm.pesquisarTiposDadosComplementares = function (tipo) {
                    amostraService.buscarTiposDadoComplementar(tipo).then(function (resp) {
                        vm.listaTiposDadosComplementares = resp.data;
                    });
                };

                vm.changeClassificacaoAmostra = function (chave) {
                    if (chave === null) {
                        vm.amostra.classificacaoAmostra = undefined;
                    }
                };

                vm.changeClassificacaoAmostraLocacao = function (chave) {
                    if (chave === null) {
                        vm.amostra.classificacaoAmostraLocacao = undefined;
                    }
                };

                vm.pesquisarEnderecoPorCep = function () {
                    if (vm.amostra.enderecamento.cep) {
                        amostraService.buscarEnderecoPorCep(vm.amostra.enderecamento.cep).then(function (resp) {
                            vm.amostra.enderecamento = resp.data;
                            vm.pesquisarMunicipiosPorUf();
                            imovelAvaliacaoService.buscarGeometria(resp.data).then(function (result) {
                                if (result.data) {
                                    NotificadorService.notify($scope, result.data, MAPA_CONSTANTS.NOTIFICADORES.CENTRALIZAR_POR_MUNICIPIO);
                                }
                            });
                            vm.cepEncontrado = true;
                        }, function () {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-cep-invalido'));
                            vm.amostra.enderecamento = {};
                            vm.cepEncontrado = false;
                        });
                    } else {
                        vm.amostra.enderecamento = {};
                    }
                };

                vm.redirecionarParaTelaAnterior = function () {
                    voltarUploadPlanilhaAmostra();
                };

                vm.redirecionarConsultarCep = function () {
                    window.open('http://www.buscacep.correios.com.br/sistemas/buscacep/', '_blank');
                };

                vm.changeValorAmotra = function (valor) {
                    if (valor >= 1) {
                        vm.valorPorExtenso = escreverPorExtenso(valor.toString());
                    } else {
                        vm.valorPorExtenso = null;
                        vm.amostra.valor = null;
                    }
                };

                vm.changeValorAmotraLocacao = function (valor) {
                    if (valor >= 1) {
                        vm.valorLocacaoPorExtenso = escreverPorExtenso(valor.toString());
                    } else {
                        vm.valorLocacaoPorExtenso = null;
                        vm.amostra.valorLocacao = null;
                    }
                };

                vm.changeAreaTerreno = function (valor) {
                    if (valor === 0) {
                        vm.amostra.areaTerreno = null;
                    }
                };

                vm.changeAreaConstruida = function (valor) {
                    if (valor === 0) {
                        vm.amostra.areaConstruida = null;
                    }
                };

                vm.limparDadosComplementares = function (aux) {
                    vm.amostra.isDadosComplementares = true;
                    if (!aux) {
                        if (vm.amostra.dadosComplementares.length > 0) {
                            var confirm = $mdDialog.confirm()
                                .title("Atenção!")
                                .textContent("Todos os dados complementares serão removidos. Deseja continuar?")
                                .ok("Confirmar")
                                .cancel("Cancelar");

                            $mdDialog.show(confirm).then(function () {
                                vm.amostra.dadosComplementares = [];
                                vm.amostra.isDadosComplementares = false;
                            }, function () {

                            });
                        } else {
                            vm.amostra.isDadosComplementares = false;
                        }
                    }
                };

                vm.limparArquivosComplementares = function (aux) {
                    vm.amostra.isArquivosComplementares = true;
                    if (!aux) {
                        if (vm.amostra.arquivosComplementares.length > 0) {
                            var confirm = $mdDialog.confirm()
                                .title("Atenção!")
                                .textContent("Todos os arquivos complementares serão removidos. Deseja continuar?")
                                .ok("Confirmar")
                                .cancel("Cancelar");

                            $mdDialog.show(confirm).then(function () {
                                vm.amostra.arquivosComplementares = [];
                                vm.amostra.isArquivosComplementares = false;
                            }, function () {

                            });
                        } else {
                            vm.amostra.isArquivosComplementares = false;
                        }
                    }
                };


                var escreverPorExtenso = function (c) {
                    var ex = [
                        ["zero", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove", "dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"],
                        ["dez", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"],
                        ["cem", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"],
                        ["mil", "milhão", "bilhão", "trilhão", "quadrilhão", "quintilhão", "sextilhão", "setilhão", "octilhão", "nonilhã", "decilhão", "undecilhão", "dodecilhão", "tredecilhão", "quatrodecilhão", "quindecilhão", "sedecilhão", "septendecilhão", "octencilhão", "nonencilhão"]
                    ];
                    var a, n, v, i, n = c.replace(c ? /[^,\d]/g : /\D/g, "").split(","), e = " e ", $ = "real",
                        d = "centavo", sl;
                    for (var f = n.length - 1, l, j = -1, r = [], s = [], t = ""; ++j <= f; s = []) {
                        j && (n[j] = (("." + n[j]) * 1).toFixed(2).slice(2));
                        if (!(a = (v = n[j]).slice((l = v.length) % 3).match(/\d{3}/g), v = l % 3 ? [v.slice(0, l % 3)] : [], v = a ? v.concat(a) : v).length) continue;
                        for (a = -1, l = v.length; ++a < l; t = "") {
                            if (!(i = v[a] * 1)) continue;
                            i % 100 < 20 && (t += ex[0][i % 100]) ||
                            i % 100 + 1 && (t += ex[1][(i % 100 / 10 >> 0) - 1] + (i % 10 ? e + ex[0][i % 10] : ""));
                            s.push((i < 100 ? t : !(i % 100) ? ex[2][i == 100 ? 0 : i / 100 >> 0] : (ex[2][i / 100 >> 0] + e + t)) +
                                ((t = l - a - 2) > -1 ? " " + (i > 1 && t > 0 ? ex[3][t].replace("?o", "?es") : ex[3][t]) : ""));
                        }
                        a = ((sl = s.length) > 1 ? (a = s.pop(), s.join(" ") + e + a) : s.join("") || ((!j && (n[j + 1] * 1 > 0) || r.length) ? "" : ex[0][0]));
                        a && r.push(a + (c ? (" " + (v.join("") * 1 > 1 ? j ? d + "s" : (/0{6,}$/.test(n[0]) ? "de " : "") + $.replace("l", "is") : j ? d : $)) : ""));
                    }
                    return r.join(e);
                };
                var desenhaBenfeitoriasMapa = function (benfeitorias) {
                    NotificadorService.notify(vm, benfeitorias, MAPA_CONSTANTS.NOTIFICADORES.DESENHAR_BENFEITORIAS);
                };
                vm.apresentaImoveisUniao = function (mostrar) {
                    if (mostrar && (vm.cepEncontrado || (vm.amostra.enderecamento && vm.amostra.enderecamento.municipio))) {
                        amostraService.benfeitoriaUniao(vm.amostra.enderecamento.municipio).then(function (resp) {
                            vm.benfeitorias = resp.data;
                            desenhaBenfeitoriasMapa(vm.benfeitorias);
                        });
                    } else {
                        NotificadorService.notify(vm, '', MAPA_CONSTANTS.NOTIFICADORES.LIMPAR_BENFEITORIAS);
                        if (vm.mostrarUniao) {
                            vm.mostrarUniao = false;
                        }
                    }

                };

                var recuperarUploadPlanilhaAmostra = function (callback) {
                    vm.amostra = avaliacaoEscopoCompartilhadoService.getObjeto('amostra');
                    vm.amostras = avaliacaoEscopoCompartilhadoService.getObjeto('amostras');
                    vm.dadoUploadAmostra = avaliacaoEscopoCompartilhadoService.getObjeto('dadoUploadAmostra');

                    avaliacaoEscopoCompartilhadoService.limparEscopo();

                    if (callback) {
                        callback();
                    }
                };

                var voltarUploadPlanilhaAmostra = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('amostras', vm.amostras);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadoUploadAmostra', vm.dadoUploadAmostra);

                    $state.go('avaliacao.UPLOAD_PLANILHA_AMOSTRA');
                };

                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('cancelarAmostraController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "comumAvaliacaoService", "$state", "formularioAvaliacaoService", "$filter", "amostraService", "ComumFactory", "usuarioAvaliacaoService", function ($scope, laudoAvaliacaoService, $stateParams, comumAvaliacaoService, $state, formularioAvaliacaoService,
                      $filter, amostraService, ComumFactory, usuarioAvaliacaoService) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.amostra = {};

                var init = function () {
                    buscarUsuarioLogado(function () {
                        if ($stateParams.idAmostra) {
                            amostraService.consultarAmostraId($stateParams.idAmostra).then(function (resp) {
                                vm.amostra = resp.data;

                                $scope.dataMostrar = $filter('date')(new Date(), 'dd/MM/yyyy');

                                comumAvaliacaoService.usuarioLogado().then(function (resp) {
                                    vm.amostra.responsavelCancelamento = resp.data.responsavelCancelamento;
                                });
                            });


                        }
                    });
                };

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_DADO_MERCADO_EXCLUIR === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                vm.cancelarAmostra = function () {
                    vm.validarFormulario(vm.formularioCancelarAmostra);

                    if (!vm.formularioCancelarAmostra.$valid) {
                        return;
                    }

                    amostraService.cancelarAmostra(vm.amostra).then(function () {
                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-dados-mercado-cancelado-sucesso'));
                        $state.go('avaliacao.CONSULTAR_AMOSTRA');
                    });
                };

                vm.redirecionarParaConsultarAmostra = function () {
                    $state.go('avaliacao.CONSULTAR_AMOSTRA');
                };

                init();
            }])

})();
(function () {
    'use strict';

    angular.module('su-avaliacao')
        .controller('adicionarVariavelAmostraController',
            ["$scope", "laudoAvaliacaoService", "$stateParams", "dominioAvaliacaoService", "$state", "formularioAvaliacaoService", "$mdDialog", "usuarioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "$filter", "amostraService", "ComumFactory", "ciMapaAvaliacaoLayersFactory", "ciMapaAvaliacaoViewFactory", "MAPA_CONSTANTS", "NotificadorService", "imovelAvaliacaoService", "$timeout", "identificacaoLaudoRelatorioService", function ($scope, laudoAvaliacaoService, $stateParams, dominioAvaliacaoService, $state, formularioAvaliacaoService, $mdDialog, usuarioAvaliacaoService, avaliacaoEscopoCompartilhadoService,
    $filter, amostraService, ComumFactory, ciMapaAvaliacaoLayersFactory, ciMapaAvaliacaoViewFactory, MAPA_CONSTANTS, NotificadorService, imovelAvaliacaoService, $timeout, identificacaoLaudoRelatorioService) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;
                vm.nomeCampo = $filter('translate')('label-amostra');
                ciMapaAvaliacaoLayersFactory.init();
                ciMapaAvaliacaoViewFactory.init();
                vm.detalharAmostra = false;
                vm.editarAmostra = false;
                vm.cepEncontrado = false;
                vm.bloquearMapa = true;
                vm.desbloquearAmostra = false;

                vm.amostra = {};
                vm.listaCondicoes = {SIM: true, NAO: false};
                vm.listaNomesVariavelCompleta = {
                    AREA_TERRENO: "Área do terreno",
                    AREA_CONSTRUIDA: "Área Construída",
                    PADRAO_ACABAMENTO: "Padrão de Acabamento",
                    ESTADO_CONSERVACAO: "Estado e Conservação",
                    ELEVADOR: "Elevador",
                    GARAGEM: "Garagem",
                    ESQUINA: "Esquina",
                    POTENCIAL_CONSTRUTIVO: "Potencial Construtivo",
                    TESTADA: "Testada",
                    TOPOGRAFIA: "Topografia",
                    VALOR_TOTAL: "Valor Total",
                    VALOR_UNITARIO: "Valor Unitário",
                    VALOR_TOTAL_LOCACAO: "Valor Total de Locação",
                    VALOR_UNITARIO_LOCACAO: "Valor Unitário de Locação",
                    OUTRO: "Outro"
                };
                vm.amostra.arquivosComplementares = [];
                vm.amostra.dadosComplementares = [];

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (vm.usuarioLogado.AVALIACAO_INCLUIR_EDITAR_DADO_MERCADO === 'false') {
                            $state.go('avaliacao.ACESSO_NEGADO');
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                var init = function () {
                    buscarUsuarioLogado(function () {
                        loadMapas(function () {

                            pesquisarTodasAmostras();
                            pesquisarUfs();
                            pesquisarTiposLogradouro();
                            pesquisarTiposAmostra();
                            pesquisarClassificacaoAmostra();
                            recuperarPropriedadesLaudoRelatorio();

                            if ($stateParams.idAmostra) {
                                amostraService.consultarAmostraId($stateParams.idAmostra).then(function (resp) {
                                    vm.amostra = resp.data;
                                    vm.changeValorAmotra(vm.amostra.valor);
                                    vm.changeValorAmotraLocacao(vm.amostra.valorLocacao);
                                    pesquisarLaudosRelatorioVinculado();
                                    buscarTiposIdentificacao();
                                    NotificadorService.notify(vm, [vm.amostra.latitude, vm.amostra.longitude], MAPA_CONSTANTS.NOTIFICADORES.CENTRALIZAR_MAPA);
                                    if ($state.current.name.indexOf('DETALHAR_AMOSTRA') !== -1) {
                                        vm.detalharAmostra = true;
                                        vm.bloquearMapa = false;
                                        if (vm.amostra.dtCancelamento != null) {
                                            vm.amostra.dtCancelamento = $filter('date')(vm.amostra.dtCancelamento, 'dd/MM/yyyy');
                                        }
                                    } else {
                                        vm.editarAmostra = true;
                                        vm.bloquearMapa = false;
                                        if(!vm.amostra.tipoAmostra){
                                            vm.desbloquearAmostra = true;
                                        }
                                        if(!vm.amostra.enderecamento.uf){
                                            vm.pesquisarEnderecoPorCep();
                                        }
                                    }

                                    if (vm.amostra.dadosComplementares && vm.amostra.dadosComplementares.length > 0) {
                                        vm.amostra.isDadosComplementares = true;
                                    } else {
                                        vm.amostra.isDadosComplementares = [];
                                        vm.amostra.isDadosComplementares = false;
                                    }

                                    if (vm.amostra.arquivosComplementares && vm.amostra.arquivosComplementares.length > 0) {
                                        vm.amostra.isArquivosComplementares = true;
                                    } else {
                                        vm.amostra.isArquivosComplementares = [];
                                        vm.amostra.isArquivosComplementares = false;
                                    }

                                });
                            }
                        });
                        function loadMapas(callback) {

                            ciMapaAvaliacaoLayersFactory.init();
                            ciMapaAvaliacaoViewFactory.init();
                            if (callback) {
                                $timeout(function () {
                                    callback();
                                });
                            }
                        };
                    });
                };

                vm.salvarAmostra = function () {
                    vm.validarFormulario(vm.formularioDadosAmostra);
                    if (!vm.formularioDadosAmostra.$valid) {
                        ComumFactory.mostrarMensagemError('Necessário preencher os campos obrigatório e indicar a localização no mapa!');
                        return;
                    }
                    if (!vm.amostra.latitude || !vm.amostra.longitude) {
                        ComumFactory.mostrarMensagemError('Necessário preencher os campos obrigatório e indicar a localização no mapa!');
                        return
                    }
                    vm.amostra.variavelAmostras = vm.variaveisAmostras;

                    $timeout(function () {
                        amostraService.salvar(vm.amostra).then(function (resp) {
                            vm.amostra = resp.data;
                            if (!($stateParams.idAmostra)) {
                                var ordem = 1;
                                if(!vm.avaliacao.amostras){
                                    vm.avaliacao.amostras = [];
                                }
                                vm.avaliacao.amostras.push(vm.amostra);
                                vm.avaliacao.amostras.forEach(function (amostra) {
                                    amostra.variavelAmostras = [];
                                    vm.avaliacao.variaveis.forEach(function (variavel) {
                                        var variavelAmostra = {};
                                        variavelAmostra.variavel = {};
                                        variavelAmostra.variavel.id = variavel.id;
                                        variavelAmostra.amostra = {};
                                        variavelAmostra.amostra.id = amostra.id;
                                        variavelAmostra.valor = variavel.valor;
                                        variavelAmostra.isAmostraUtilizada = vm.avaliacao.variaveis[0].isAmostraUtilizada;
                                        variavelAmostra.ordem = ordem;
                                        variavel.valor = null;
                                        amostra.variavelAmostras.push(variavelAmostra);
                                    });
                                    ordem ++;
                                });
                                amostraService.selecionarAmostra(vm.avaliacao.amostras).then(function () {
                                    voltarCadastrarLaudoRelatorio();
                                    ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                });
                            }else{
                                voltarCadastrarLaudoRelatorio();
                                ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                            }
                        });
                    });




                };

                var pesquisarUfs = function () {
                    dominioAvaliacaoService.pesquisarUfsIntegrador().then(function (resp) {
                        vm.listaUFs = resp.data;
                    });
                };

                var pesquisarTodasAmostras = function () {
                    amostraService.allAmostras().then(function (resp) {
                        var amostras = resp.data;
                        for (var i = 0; i < amostras.length; i++) {
                            if (amostras[i].id === Number($stateParams.idAmostra)) {
                                amostras[i].pinAmarelo = true;
                                break;
                            }
                        }
                        NotificadorService.notify(vm, amostras, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_AMOSTRAS);
                    });
                };

                vm.pesquisarMunicipiosPorUf = function () {
                    if (vm.amostra.enderecamento.uf) {
                        dominioAvaliacaoService.pesquisarMuniciposPorUfIntegrador(vm.amostra.enderecamento.uf).then(function (resposta) {
                            vm.listaMunicipios = resposta.data;
                        });
                    }
                };

                var pesquisarTiposLogradouro = function () {
                    dominioAvaliacaoService.consultarTiposLogradouroSU().then(function (resp) {
                        vm.listaTiposLogradouro = resp.data;
                    });
                };

                var pesquisarTiposAmostra = function () {
                    vm.listaTiposAmostra = [];
                    amostraService.buscarTiposAmostra().then(function (resp) {
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaTiposAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                    });
                };

                var pesquisarClassificacaoAmostra = function () {
                    amostraService.buscarClassificacaoAmostra().then(function (resp) {
                        vm.listaClassificacaoAmostra = [];
                        angular.forEach(resp.data, function (value, key) {
                            vm.listaClassificacaoAmostra.push({
                                chave: key,
                                valor: value
                            });
                        });
                        vm.listaClassificacaoAmostra.push({
                            chave: null,
                            valor: null
                        });
                    });
                };

                vm.pesquisarTiposDadosComplementares = function (tipo) {
                    amostraService.buscarTiposDadoComplementar(tipo).then(function (resp) {
                        vm.listaTiposDadosComplementares = resp.data;
                    });
                };

                vm.changeClassificacaoAmostra = function (chave) {
                    if (chave === null) {
                        vm.amostra.classificacaoAmostra = undefined;
                    }
                };

                vm.changeClassificacaoAmostraLocacao = function (chave) {
                    if (chave === null) {
                        vm.amostra.classificacaoAmostraLocacao = undefined;
                    }
                };

                vm.pesquisarEnderecoPorCep = function () {
                    if (vm.amostra.enderecamento.cep) {
                        amostraService.buscarEnderecoPorCep(vm.amostra.enderecamento.cep).then(function (resp) {
                            vm.amostra.enderecamento = resp.data;
                            vm.pesquisarMunicipiosPorUf();
                            imovelAvaliacaoService.buscarGeometria(resp.data).then(function (result) {
                                if (result.data) {
                                    NotificadorService.notify($scope, result.data, MAPA_CONSTANTS.NOTIFICADORES.CENTRALIZAR_POR_MUNICIPIO);
                                }
                            });
                            vm.cepEncontrado = true;
                        }, function () {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-cep-invalido'));
                            vm.amostra.enderecamento = {};
                            vm.cepEncontrado = false;
                        });
                    } else {
                        vm.amostra.enderecamento = {};
                    }
                };

                vm.redirecionarParaTelaAnterior = function () {
                    voltarCadastrarLaudoRelatorio();
                };

                vm.redirecionarConsultarCep = function () {
                    window.open('http://www.buscacep.correios.com.br/sistemas/buscacep/', '_blank');
                };

                vm.changeValorAmotra = function (valor) {
                    if (valor >= 1) {
                        vm.valorPorExtenso = escreverPorExtenso(valor.toString());
                    } else {
                        vm.valorPorExtenso = null;
                        vm.amostra.valor = null;
                    }
                };

                vm.changeValorAmotraLocacao = function (valor) {
                    if (valor >= 1) {
                        vm.valorLocacaoPorExtenso = escreverPorExtenso(valor.toString());
                    } else {
                        vm.valorLocacaoPorExtenso = null;
                        vm.amostra.valorLocacao = null;
                    }
                };

                vm.changeAreaTerreno = function (valor) {
                    if (valor == 0) {
                        vm.amostra.areaTerreno = null;
                    }
                };

                vm.changeAreaConstruida = function (valor) {
                    if (valor == 0) {
                        vm.amostra.areaConstruida = null;
                    }
                };

                vm.limparDadosComplementares = function (aux) {
                    vm.amostra.isDadosComplementares = true;
                    if (!aux) {
                        if (vm.amostra.dadosComplementares.length > 0) {
                            var confirm = $mdDialog.confirm()
                                .title("Atenção!")
                                .textContent("Todos os dados complementares serão removidos. Deseja continuar?")
                                .ok("Confirmar")
                                .cancel("Cancelar");

                            $mdDialog.show(confirm).then(function () {
                                vm.amostra.dadosComplementares = [];
                                vm.amostra.isDadosComplementares = false;
                            }, function () {

                            });
                        }else{
                            vm.amostra.isDadosComplementares = false;
                        }
                    }
                };

                vm.limparArquivosComplementares = function (aux) {
                    vm.amostra.isArquivosComplementares = true;
                    if (!aux) {
                        if (vm.amostra.arquivosComplementares.length > 0) {
                            var confirm = $mdDialog.confirm()
                                .title("Atenção!")
                                .textContent("Todos os arquivos complementares serão removidos. Deseja continuar?")
                                .ok("Confirmar")
                                .cancel("Cancelar");

                            $mdDialog.show(confirm).then(function () {
                                vm.amostra.arquivosComplementares = [];
                                vm.amostra.isArquivosComplementares = false;
                            }, function () {

                            });
                        }else{
                            vm.amostra.isArquivosComplementares = false;
                        }
                    }
                };


                var escreverPorExtenso = function (c) {
                    var ex = [
                        ["zero", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove", "dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"],
                        ["dez", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"],
                        ["cem", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"],
                        ["mil", "milhão", "bilhão", "trilhão", "quadrilhão", "quintilhão", "sextilhão", "setilhão", "octilhão", "nonilhã", "decilhão", "undecilhão", "dodecilhão", "tredecilhão", "quatrodecilhão", "quindecilhão", "sedecilhão", "septendecilhão", "octencilhão", "nonencilhão"]
                    ];
                    var a, n, v, i, n = c.replace(c ? /[^,\d]/g : /\D/g, "").split(","), e = " e ", $ = "real",
                        d = "centavo", sl;
                    for (var f = n.length - 1, l, j = -1, r = [], s = [], t = ""; ++j <= f; s = []) {
                        j && (n[j] = (("." + n[j]) * 1).toFixed(2).slice(2));
                        if (!(a = (v = n[j]).slice((l = v.length) % 3).match(/\d{3}/g), v = l % 3 ? [v.slice(0, l % 3)] : [], v = a ? v.concat(a) : v).length) continue;
                        for (a = -1, l = v.length; ++a < l; t = "") {
                            if (!(i = v[a] * 1)) continue;
                            i % 100 < 20 && (t += ex[0][i % 100]) ||
                            i % 100 + 1 && (t += ex[1][(i % 100 / 10 >> 0) - 1] + (i % 10 ? e + ex[0][i % 10] : ""));
                            s.push((i < 100 ? t : !(i % 100) ? ex[2][i == 100 ? 0 : i / 100 >> 0] : (ex[2][i / 100 >> 0] + e + t)) +
                                ((t = l - a - 2) > -1 ? " " + (i > 1 && t > 0 ? ex[3][t].replace("?o", "?es") : ex[3][t]) : ""));
                        }
                        a = ((sl = s.length) > 1 ? (a = s.pop(), s.join(" ") + e + a) : s.join("") || ((!j && (n[j + 1] * 1 > 0) || r.length) ? "" : ex[0][0]));
                        a && r.push(a + (c ? (" " + (v.join("") * 1 > 1 ? j ? d + "s" : (/0{6,}$/.test(n[0]) ? "de " : "") + $.replace("l", "is") : j ? d : $)) : ""));
                    }
                    return r.join(e);
                };
                var desenhaBenfeitoriasMapa = function (benfeitorias) {
                    NotificadorService.notify(vm, benfeitorias, MAPA_CONSTANTS.NOTIFICADORES.DESENHAR_BENFEITORIAS);
                };
                vm.apresentaImoveisUniao = function (mostrar) {
                    if (mostrar && (vm.cepEncontrado || (vm.amostra.enderecamento && vm.amostra.enderecamento.municipio ))) {
                        amostraService.benfeitoriaUniao(vm.amostra.enderecamento.municipio).then(function (resp) {
                            vm.benfeitorias = resp.data;
                            desenhaBenfeitoriasMapa(vm.benfeitorias);
                        });
                    } else {
                        NotificadorService.notify(vm, '', MAPA_CONSTANTS.NOTIFICADORES.LIMPAR_BENFEITORIAS);
                        if (vm.mostrarUniao) {
                            vm.mostrarUniao = false;
                        }
                    }

                };

                var recuperarPropriedadesLaudoRelatorio = function () {
                    vm.laudoRelatorioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('laudoRelatorioAvaliacao');
                    vm.dadosEnderecamento = avaliacaoEscopoCompartilhadoService.getObjeto('dadosEnderecamento');
                    vm.dadosProprietario = avaliacaoEscopoCompartilhadoService.getObjeto('dadosProprietario');
                    vm.benfeitoria = avaliacaoEscopoCompartilhadoService.getObjeto('benfeitoria');
                    vm.tipoTerreno = avaliacaoEscopoCompartilhadoService.getObjeto('tipoTerreno');

                    /**Identificacao**/
                    vm.ripImovel = avaliacaoEscopoCompartilhadoService.getObjeto('ripImovel');
                    vm.pontoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('pontoImovel');
                    vm.listaResponsaveisTecnicos = avaliacaoEscopoCompartilhadoService.getObjeto('listaResponsaveisTecnicos');
                    vm.listaNormasUtilizadas = avaliacaoEscopoCompartilhadoService.getObjeto('listaNormasUtilizadas');
                    /**Detalher Imovel**/
                    vm.listaMatriculasTranscricao = avaliacaoEscopoCompartilhadoService.getObjeto('listaMatriculasTranscricao');
                    vm.listaCertidoesCartoriais = avaliacaoEscopoCompartilhadoService.getObjeto('listaCertidoesCartoriais');
                    angular.forEach(vm.listaCertidoesCartoriais, function (certidaoCartorial) {
                        certidaoCartorial.arquivo.id = null;
                    });
                    vm.imovel = avaliacaoEscopoCompartilhadoService.getObjeto('imovel');
                    vm.listaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosImovel');
                    /**Terreno Imovel**/
                    vm.terreno = avaliacaoEscopoCompartilhadoService.getObjeto('terreno');
                    vm.documentosParametrosUrbanisticos = avaliacaoEscopoCompartilhadoService.getObjeto('documentosParametrosUrbanisticos');
                    /**Benfeitorias**/
                    vm.listaBenfeitoriaAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('listaBenfeitoriaAvaliacao');
                    vm.listaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosBenfeitorias');
                    /**Unidade Autonoma**/
                    vm.unidadeAutonomaIncorporacao = avaliacaoEscopoCompartilhadoService.getObjeto('unidadeAutonomaIncorporacao');
                    vm.listaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('listaFotosUnidadeAutonoma');
                    /**Vistoria**/
                    vm.vistoriaListaFotosImovel = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosImovel');
                    vm.vistoriaListaFotosBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosBenfeitorias');
                    vm.vistoriaListaFotosUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('vistoriaListaFotosUnidadeAutonoma');
                    /**Avaliacao**/
                    vm.tabSelect = avaliacaoEscopoCompartilhadoService.getObjeto('tabSelect');
                    vm.avaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('avaliacao');
                    vm.variaveisAmostras = avaliacaoEscopoCompartilhadoService.getObjeto('variaveisAmostras');

                    vm.formularioIdentificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioIdentificacao');
                    vm.formularioDetalhesImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioDetalhesImovel');
                    vm.formularioTerrenoImovel = avaliacaoEscopoCompartilhadoService.getObjeto('formularioTerrenoImovel');
                    vm.formularioBenfeitorias = avaliacaoEscopoCompartilhadoService.getObjeto('formularioBenfeitorias');
                    vm.formularioUnidadeAutonoma = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUnidadeAutonoma');
                    vm.formularioUtilizacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioUtilizacao');
                    vm.formularioVistoria = avaliacaoEscopoCompartilhadoService.getObjeto('formularioVistoria');
                    vm.formularioAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAvaliacao');
                    vm.formularioResultadosRelatorio = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosRelatorio');
                    vm.formularioResultadosLaudo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadosLaudo');
                    vm.formularioQuantificacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacao');
                    vm.formularioQuantificacaoOrcamento = avaliacaoEscopoCompartilhadoService.getObjeto('formularioQuantificacaoOrcamento');
                    vm.formularioResultadoAvaliacao = avaliacaoEscopoCompartilhadoService.getObjeto('formularioResultadoAvaliacao');
                    vm.formularioAnexo = avaliacaoEscopoCompartilhadoService.getObjeto('formularioAnexo');
                    avaliacaoEscopoCompartilhadoService.limparEscopo();
                };

                var voltarCadastrarLaudoRelatorio = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('laudoRelatorioAvaliacao', vm.laudoRelatorioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosEnderecamento', vm.dadosEnderecamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('dadosProprietario', vm.dadosProprietario);
                    /**Identificacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('ripImovel', vm.ripImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('pontoImovel', vm.pontoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaResponsaveisTecnicos', vm.listaResponsaveisTecnicos);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaNormasUtilizadas', vm.listaNormasUtilizadas);
                    /**Detalher Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaMatriculasTranscricao', vm.listaMatriculasTranscricao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaCertidoesCartoriais', vm.listaCertidoesCartoriais);
                    avaliacaoEscopoCompartilhadoService.setObjetos('imovel', vm.imovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosImovel', vm.listaFotosImovel);
                    /**Terreno Imovel**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('terreno', vm.terreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('documentosParametrosUrbanisticos', vm.documentosParametrosUrbanisticos);
                    /**Benfeitorias**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaBenfeitoriaAvaliacao', vm.listaBenfeitoriaAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosBenfeitorias', vm.listaFotosBenfeitorias);
                    /**Unidade Autonoma**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('unidadeAutonomaIncorporacao', vm.unidadeAutonomaIncorporacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('listaFotosUnidadeAutonoma', vm.listaFotosUnidadeAutonoma);
                    /**Vistoria**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosImovel', vm.vistoriaListaFotosImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosBenfeitorias', vm.vistoriaListaFotosBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('vistoriaListaFotosUnidadeAutonoma', vm.vistoriaListaFotosUnidadeAutonoma);
                    /**Avaliacao**/
                    avaliacaoEscopoCompartilhadoService.setObjetos('avaliacao', vm.avaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tipoTerreno', vm.tipoTerreno);
                    avaliacaoEscopoCompartilhadoService.setObjetos('benfeitoria', vm.benfeitoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('tabSelect', vm.tabSelect);
                    avaliacaoEscopoCompartilhadoService.setObjetos('variaveisAmostras', vm.variaveisAmostras);

                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioIdentificacao', vm.formularioIdentificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioDetalhesImovel', vm.formularioDetalhesImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioTerrenoImovel', vm.formularioTerrenoImovel);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioBenfeitorias', vm.formularioBenfeitorias);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUnidadeAutonoma', vm.formularioUnidadeAutonoma);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioUtilizacao', vm.formularioUtilizacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioVistoria', vm.formularioVistoria);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAvaliacao', vm.formularioAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosRelatorio', vm.formularioResultadosRelatorio);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadosLaudo', vm.formularioResultadosLaudo);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacao', vm.formularioQuantificacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioQuantificacaoOrcamento', vm.formularioQuantificacaoOrcamento);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioResultadoAvaliacao', vm.formularioResultadoAvaliacao);
                    avaliacaoEscopoCompartilhadoService.setObjetos('formularioAnexo', vm.formularioAnexo);

                    $state.go('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                        idLaudo: vm.laudoRelatorioAvaliacao.id
                    });
                };

                vm.validarValorLaudoRelatorio = function (tipoValor, resultadoAvaliacao) {
                    if(tipoValor){
                        if (tipoValor === 'VALOR_MEDIO') {
                            if(resultadoAvaliacao.valorMedio){
                                return resultadoAvaliacao.moeda.simbolo + ' ' + $filter('currency')(resultadoAvaliacao.valorMedio).replace('R$', '');
                            }

                        } else if (tipoValor === 'VALOR_MINIMO') {
                            if(resultadoAvaliacao.valorMinimo){
                                return resultadoAvaliacao.moeda.simbolo + ' ' + $filter('currency')(resultadoAvaliacao.valorMinimo).replace('R$', '');
                            }

                        } else if (tipoValor === 'VALOR_MAXIMO') {
                            if(resultadoAvaliacao.valorMaximo){
                                return resultadoAvaliacao.moeda.simbolo + ' ' + $filter('currency')(resultadoAvaliacao.valorMaximo).replace('R$', '');
                            }

                        } else if (tipoValor === 'OUTRO_VALOR') {
                            if(resultadoAvaliacao.outroValorAdotado){
                                return resultadoAvaliacao.moeda.simbolo + ' ' + $filter('currency')(resultadoAvaliacao.outroValorAdotado).replace('R$', '');
                            }
                        }
                    }
                };

                var pesquisarLaudosRelatorioVinculado = function () {
                    amostraService.buscarLaudosRelatorioVinculado(vm.amostra.id).then(function (resp) {
                        vm.paginationDadosUtilizacao = resp.data;
                    });
                };

                var buscarTiposIdentificacao = function () {
                    identificacaoLaudoRelatorioService.buscarTiposIdentificacao().then(function (resp) {
                        vm.tiposIdentificacao = resp.data;
                    });
                };

                vm.ripImovelLink = function (rip) {
                    window.open('#/incorporacao/detalharImovel/' + rip, '_blank');
                };

                vm.redirecionarDetalharLaudoAvaliacao = function (id) {
                    window.open('#/avaliacao/detalharLaudoRelatorioAvaliacao/' + id, '_blank');
                };

                init();
            }])

})();
(function () {
    directive.$inject = ["ol", "$document", "$timeout", "mapaAvaliacaoService"];
    angular
        .module('su-avaliacao')
        .directive('mapaDetalharLocalizacoesImoveis', directive);

    function directive(ol, $document, $timeout, mapaAvaliacaoService) {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-avaliacao/app/directives/utilitarios/templates/mapaDetalharImovel.html',
            scope: {
                imoveis: '=',
                imagens: '=',
                listaDadosImoveisAvaliacao: '='
            },
            link: function ($scope, element) {
                $timeout(function () {
                    var mapElement = element[0].querySelector('.map');
                    var elementOverlay = element[0].querySelector('#popup');

                    if (!mapElement) {
                        console.error('Elemento do mapa não encontrado');
                        return;
                    }
                    if (!elementOverlay) {
                        console.error('Elemento popup não encontrado');
                        return;
                    }

                    var options = mapaAvaliacaoService.getOptions();
                    var fullScreenControl = new ol.control.FullScreen(options);
                    var zoomControl = new ol.control.Zoom(options);

                    var overlay = mapaAvaliacaoService.getOverlay(elementOverlay);

                    var source = mapaAvaliacaoService.getSource();
                    var iconStyle = new ol.style.Style({
                        image: mapaAvaliacaoService.getIcon(),
                        fill: mapaAvaliacaoService.getFill(),
                        stroke: mapaAvaliacaoService.getStroke()
                    });

                    var geoJson = new ol.format.GeoJSON({
                        defaultProjection: 'EPSG:4674'
                    });

                    var vector = mapaAvaliacaoService.getVector(source, iconStyle);
                    var view = new ol.View({
                        center: ol.proj.fromLonLat([-48.0556, -16.0193]),
                        zoom: 2,
                        minZoom: 4,
                        maxZoom: 20,
                        projection: "EPSG:3857"
                    });

                    var OSM = mapaAvaliacaoService.getTileOSM();
                    var layers = [OSM, vector];

                    var map = new ol.Map({
                        target: mapElement,
                        layers: layers,
                        view: view,
                        controls: ol.control.defaults().extend([
                            fullScreenControl,
                            zoomControl
                        ]),
                        overlays: [overlay]
                    });

                    function TerrenoVerde() {
                        return new ol.style.Style({
                            fill: new ol.style.Fill({
                                color: 'rgba(20, 236, 0, 0.1)'
                            }),
                            stroke: new ol.style.Stroke({
                                color: '#14ec00',
                                width: 3
                            }),
                            zIndex: 1
                        });
                    }

                    function desenharBenfeitoria(benfeitoria) {
                        if (benfeitoria.geometria) {
                            var feature = new ol.Feature({
                                geometry: geoJson.readGeometry(benfeitoria.geometria)
                            });
                            feature.setStyle(TerrenoVerde());
                            feature.setId("benfeitoria_" + benfeitoria.id);
                            source.addFeature(feature);
                        }
                    }

                    function desenharTerreno(benfeitoria) {
                        if (benfeitoria.terreno) {
                            var feature = new ol.Feature({
                                geometry: geoJson.readGeometry(benfeitoria.terreno)
                            });
                            feature.setStyle(TerrenoVerde());
                            feature.setId("terreno_" + benfeitoria.id);
                            source.addFeature(feature);
                        }
                    }

                    function marcarPonto(listaDados) {
                        source.clear();

                        angular.forEach(listaDados, function (dadosImovel) {
                            var featurePonto = mapaAvaliacaoService.criarFeature(dadosImovel.pontoImovel);
                            var geometry = featurePonto.getGeometry();
                            if (geometry) {
                                geometry.dadosImovel = dadosImovel;
                            } else {
                                console.warn('Não foi encontrado dados de geometria para o RIP: ', dadosImovel.rip);
                            }

                            source.addFeature(featurePonto);

                            if (dadosImovel.benfeitoria) {
                                desenharBenfeitoria(dadosImovel.benfeitoria);
                                desenharTerreno(dadosImovel.benfeitoria);
                            }
                        });

                        $timeout(function () {
                            map.updateSize();
                            var extent = source.getExtent();

                            if (!ol.extent.isEmpty(extent)) {
                                map.getView().fit(extent, {
                                    size: map.getSize()
                                });

                                var currentZoom = map.getView().getZoom();
                                if (currentZoom > 12) {
                                    map.getView().setZoom(12);
                                }

                            } else {
                                map.getView().setCenter(ol.proj.fromLonLat([-48.0556, -16.0193]));
                                map.getView().setZoom(4);
                            }
                        }, 300);
                    }

                    function mostrarOverlay(feature) {
                        if (feature) {
                            var coordinates = feature.getGeometry().getCoordinates();
                            elementOverlay.innerHTML = getTemplateOverlay(feature.getGeometry().dadosImovel);
                            elementOverlay.style.display = 'block';
                            overlay.setPosition(coordinates);
                        } else {
                            elementOverlay.style.display = 'none';
                        }
                    }

                    function mouseHand(feature) {
                        var target = angular.element(map.getTargetElement());
                        target.css('cursor', feature ? 'pointer' : '');
                    }

                    var getTemplateOverlay = function (dadosImovel) {
                        return mapaAvaliacaoService.getTemplateOverlay(dadosImovel);
                    };

                    map.on('click', function (event) {
                        var feature = map.forEachFeatureAtPixel(event.pixel, function (feature) {
                            return feature;
                        });
                        mostrarOverlay(feature);
                        mouseHand(feature);
                    });

                    map.on('pointermove', function (event) {
                        var feature = map.forEachFeatureAtPixel(event.pixel, function (feature) {
                            return feature;
                        });
                        mouseHand(feature);
                    });

                    if ($scope.listaDadosImoveisAvaliacao && $scope.listaDadosImoveisAvaliacao.length) {
                        marcarPonto($scope.listaDadosImoveisAvaliacao);
                    }

                    $scope.$watchCollection('listaDadosImoveisAvaliacao', function (newVal) {
                        if (newVal && newVal.length) {
                            marcarPonto(newVal);
                        }
                    });

                    $scope.$on('$destroy', function () {
                        map.setTarget(null);
                        vector.getSource().clear();
                        map.getLayers().clear();
                    });
                });
            }
        };
    }
})();

(function () {
    directive.$inject = ["ol", "$document", "mapaAvaliacaoService", "$timeout"];
    angular
        .module('su-avaliacao')
        .directive('mapaDetalharImovel', directive);
    function directive (ol, $document, mapaAvaliacaoService, $timeout) {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-avaliacao/app/directives/utilitarios/templates/mapaDetalharImovel.html',
            scope: {
                pontoImovel: '=',
                dadosImovelAvaliacao: '=',
                geometriaTerreno: '=',
                benfeitorias: '='
            },
            link: function ($scope) {
                $scope.erroMapa = false;

                var options = mapaAvaliacaoService.getOptions();
                var fullScreenControl = new ol.control.FullScreen(options);
                var zoomControl = new ol.control.Zoom(options);
                var elementOverlay = $document[0].getElementById('popup');

                var overlay = mapaAvaliacaoService.getOverlay(elementOverlay);

                var source = mapaAvaliacaoService.getSource();
                var iconStyle = new ol.style.Style({
                    image: mapaAvaliacaoService.getIcon(),
                    fill: mapaAvaliacaoService.getFill(),
                    stroke: mapaAvaliacaoService.getStroke(),
                    zIndex: 9
                });

                var vector = mapaAvaliacaoService.getVector(source, iconStyle);
                var view = mapaAvaliacaoService.getViewPadrao();
                var OSM = mapaAvaliacaoService.getTileOSM();
                var layers = [OSM, vector];
                var map = mapaAvaliacaoService.getMap(layers, view);
                var geoJson = new ol.format.GeoJSON({
                    defaultProjection: 'EPSG:4674'
                });
                map.addControl(fullScreenControl);
                map.addControl(zoomControl);
                map.addOverlay(overlay);
                var terrenoStyle = new ol.style.Style({
                    fill: new ol.style.Fill({
                        color: 'rgba(20, 236, 0, 0.1)'
                    }),
                    stroke: new ol.style.Stroke({
                        color: '#14ec00',
                        width: 3
                    }),
                    zIndex: 1
                });

                function marcarPonto(pontoImovel) {
                    var featurePonto = mapaAvaliacaoService.criarFeature(pontoImovel);
                    var geometry = featurePonto.getGeometry();
                    if (geometry) {
                        geometry.dadosImovel = $scope.dadosImovelAvaliacao;
                        source.addFeature(featurePonto);
                        map.getView().fit(source.getExtent(), map.getSize());
                        map.getView().setZoom(18);
                    } else {
                        console.warn('Erro: Falha ao carregar os dados de geometria do imóvel.');
                        $scope.erroMapa = true;
                        $scope.$applyAsync();
                    }
                }

                function desenharGeometria(geometria){
                    if (geometria) {
                        var feature = new ol.Feature({
                            geometry: geoJson.readGeometry(geometria)
                        });
                        feature.setStyle(terrenoStyle);
                        source.addFeature(feature);
                    }
                }

                function desenharBenfeitorias(benfeitorias){
                    if (benfeitorias && benfeitorias.length > 0) {
                        angular.forEach(benfeitorias, function (benfeitoria) {
                            var feature;
                            feature = new ol.Feature({
                                geometry: geoJson.readGeometry(benfeitoria.geometriaBenfeitoria)
                            });
                            feature.setStyle(terrenoStyle);
                            feature.setId(benfeitoria.codigo);
                            feature.set('name', 'benfeitoria');
                            source.addFeature(feature);
                        });
                    }
                }

                function mostrarOverlay (feature) {
                    if (feature) {
                        var coordinates = feature.getGeometry().getCoordinates();
                        elementOverlay.innerHTML = getTemplateOverlay(feature.getGeometry().dadosImovel);
                        elementOverlay.style.display = 'block';
                        overlay.setPosition(coordinates);
                    } else {
                        if (overlay) {
                            elementOverlay.style.display = 'none';
                        }
                    }
                }

                function mouseHand (feature) {
                    var target = angular.element(map.getTargetElement());
                    if (feature) {
                        target.css('cursor', 'pointer');
                    } else {
                        target.css('cursor', '');
                    }
                }

                var getTemplateOverlay = function(dadosImovelAvaliacao) {
                    return mapaAvaliacaoService.getTemplateOverlay(dadosImovelAvaliacao);
                };

                map.on('click', function(event) {
                    var feature = map.forEachFeatureAtPixel(event.pixel, function (feature) {
                        return feature;
                    });
                    mostrarOverlay(feature);
                    mouseHand(feature);
                });

                map.on('pointermove', function (event) {
                    var feature = map.forEachFeatureAtPixel(event.pixel, function (feature) {
                        return feature;
                    });
                    mouseHand(feature);
                });

                var init = function() {
                    angular.element(document).ready(function () {
                        vector.getSource().clear();
                        marcarPonto($scope.pontoImovel);

                        if ($scope.erroMapa) {
                            return;
                        }

                        desenharGeometria($scope.geometriaTerreno);
                        desenharBenfeitorias($scope.benfeitorias);

                        // Aguarda 200ms antes de atualizar o tamanho do mapa para evitar falha de renderização no Chrome
                        $timeout(function () {
                            map.updateSize();
                        }, 200);
                    });
                };

                init();
            }
        };
    }
})();

(function () {
    directive.$inject = ["ol", "$document", "mapaAvaliacaoService"];
    angular
        .module('su-avaliacao')
        .directive('mapaDetalharImovelAvaliacao', directive);
    function directive (ol, $document, mapaAvaliacaoService) {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-avaliacao/app/directives/utilitarios/templates/mapaDetalharImovelAvaliacao.html',
            scope: {
                pontoImovel: '=',
                capturarMapa: '='
            },
            link: function ($scope) {
                angular.element(document).ready(function () {
                    var options = mapaAvaliacaoService.getOptions();
                    var fullScreenControl = new ol.control.FullScreen(options);
                    var zoomControl = new ol.control.Zoom(options);
                    var elementOverlay = $document[0].getElementById('popup');

                    var overlay = mapaAvaliacaoService.getOverlay(elementOverlay);

                    var source = mapaAvaliacaoService.getSource();
                    var iconStyle = new ol.style.Style({
                        image: mapaAvaliacaoService.getIcon(),
                        fill: mapaAvaliacaoService.getFill(),
                        stroke: mapaAvaliacaoService.getStroke()
                    });

                    var vector = mapaAvaliacaoService.getVector(source, iconStyle);
                    var view = mapaAvaliacaoService.getViewPadrao();
                    var OSM = mapaAvaliacaoService.getTileOSM();
                    var layers = [OSM, vector];
                    var map = mapaAvaliacaoService.getMap(layers, view);

                    map.addControl(fullScreenControl);
                    map.addControl(zoomControl);
                    map.addOverlay(overlay);

                    $scope.$watch('pontoImovel', function (value) {
                        if (value !== undefined && value !== "") {
                            vector.getSource().clear();
                            marcarPonto($scope.pontoImovel);
                            map.updateSize();
                        }
                    });

                    function marcarPonto (pontoImovel) {
                        var featurePonto = mapaAvaliacaoService.criarFeature(pontoImovel);
                        source.addFeature(featurePonto);
                        map.getView().fit(source.getExtent(), /* @type {ol.Size} */ (map.getSize()));
                        map.getView().setZoom(15);
                    }

                    map.on('click', function(event) {
                        var feature = map.forEachFeatureAtPixel(event.pixel, function (feature) {
                            return feature;
                        });
                        /*$scope.imagens = angular.copy(feature.getGeometry().imovel.imagens);
                         $scope.$apply();*/
                        mouseHand(feature);
                    });

                    map.on('pointermove', function (event) {
                        var feature = map.forEachFeatureAtPixel(event.pixel, function (feature) {
                            return feature;
                        });
                        mouseHand(feature);
                    });

                    function mouseHand (feature) {
                        var target = angular.element(map.getTargetElement());
                        if (feature) {
                            target.css('cursor', 'pointer');
                        } else {
                            target.css('cursor', '');
                        }
                    }

                    $scope.capturarMapa = function(){
                        var imagemLocalizacao = null;
                        map.once('postcompose', function(event) {
                            var canvas = event.context.canvas;
                            imagemLocalizacao = canvas.toDataURL('image/png').replace('data:image/png;base64,', '');
                        });
                        map.renderSync();
                        return imagemLocalizacao;
                    };
                });
            }
        };
    }
})();
(function () {
    angular
        .module('su-avaliacao')
        .directive('manterEdicaoCampo', directive);
    function directive () {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-avaliacao/app/directives/utilitarios/templates/manterEdicaoCampo.tmpl.html',
            scope: {
                valor: '=',
                isCampo: '='
            },
            link: function ($scope) {

                var vm = $scope;
                vm.antigoValor = null;

                vm.$watch('valor', function() {
                    if (!vm.antigoValor) {
                        vm.antigoValor = angular.copy(vm.valor);
                    }
                });

                vm.salvarEdicaoCampo = function() {
                    vm.antigoValor = vm.valor;
                    if (vm.isCampo){
                        return false;
                    }
                    return true
                };

                vm.cancelarEdicaoCampo = function() {
                    vm.valor = vm.antigoValor;
                    if (vm.isCampo){
                        return false;
                    }
                    return true
                }
            }
        };
    }
})();
(function () {
    angular
        .module('su-avaliacao')
        .directive('habilitarEdicaoCampo', directive);
    function directive () {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-avaliacao/app/directives/utilitarios/templates/habilitarEdicaoCampo.tmpl.html',
            scope: {
                isCampo: '='
            },
            link: function ($scope) {

                var vm = $scope;

                vm.habilitarEdicaoCampo = function() {
                    vm.isCampo = false;
                };
            }
        };
    }
})();
(function () {
    directive.$inject = ["contadorRomanoService"];
    angular
        .module('su-avaliacao')
        .directive('contadorRomano', directive);
    function directive (contadorRomanoService) {
        return {
            restrict: 'EA',
            templateUrl: 'scripts-avaliacao/app/directives/utilitarios/templates/contadorRomano.tmpl.html',
            scope: {
                order: '=?',
                number: '=?',
                parent: '=?'
            },
            link: function ($scope) {

                var vm = $scope;

                vm.number = {value: null};

                vm.$watchGroup(['order', 'parent', 'number'], function(){
                    if(vm.order){
                        contadorRomanoService.orderCount(vm.number, vm.order, vm.parent);
                    } else if(vm.parent) {
                        contadorRomanoService.orderCount(vm.number, 1, vm.parent);
                    } else {
                        vm.number = {value: contadorRomanoService.count()};
                    }
                });


            }
        };
    }
})();
(function () {

    directive.$inject = ["comumAvaliacaoService", "ComumFactory", "$filter"];
    angular.module('su-avaliacao')
        .directive('tableInformarValorLaudoUnidadeAutonoma', directive);

    function directive (comumAvaliacaoService, ComumFactory, $filter) {
        return {
            restrict: 'EA',
            scope: {
                unidadeAutonomaImovel: '=',
                detalharLaudoRelatorio: '=',
                nomeCampo: '=',
                redirecionarInformarValorCub: '=',
                tipoValorLaudo: '=',
                valorTerreno: '=',
                totalValorTerreno: '=',
                valorAdotadoSelecionado: '=',
                moeda: '=',
                listaMoedas: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/tabelas/templates/tableInformarValorLaudoUnidadeAutonoma.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];

                vm.TIPO_UNIDADE_AUTONOMA = 'TIPO_UNIDADE_AUTONOMA';

                var init = function() {
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                };

                var pesquisarPadroesAcabamento = function() {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function(resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function() {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function(resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                vm.goInformarValorCub = function() {
                    vm.redirecionarInformarValorCub(null, vm.TIPO_UNIDADE_AUTONOMA);
                };

                vm.calcularValorTerreno = function() {
                    if (vm.tipoValorLaudo === 'INFORMAR_VALORES_LAUDO') {
                        if (vm.unidadeAutonomaImovel && vm.unidadeAutonomaImovel.length === 1) {
                            vm.valorTerreno = vm.valorAdotadoSelecionado - vm.unidadeAutonomaImovel[0].valor;
                            vm.totalValorTerreno = vm.unidadeAutonomaImovel[0].valor;
                            if(vm.valorTerreno < 0){
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                                vm.unidadeAutonomaImovel[0].valor = 0;
                                return;
                            }
                        }
                    }
                };

                init();
            }
        };
    }

})();
(function () {

    directive.$inject = ["comumAvaliacaoService", "$mdMedia", "$mdDialog", "$filter", "ComumFactory"];
    angular.module('su-avaliacao')
        .directive('tableInformarValorLaudoRelatorioBenfeitorias', directive);

    function directive(comumAvaliacaoService, $mdMedia, $mdDialog, $filter, ComumFactory) {
        return {
            restrict: 'EA',
            scope: {
                benfeitoriasImovel: '=',
                detalharLaudoRelatorio: '=',
                nomeCampo: '=',
                redirecionarInformarValorCub: '=',
                valorAdotadoSelecionado: '=',
                tipoValorLaudo: '=',
                valorTerreno: '=',
                totalValorTerreno: '=',
                moeda: '=',
                listaMoedas: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/tabelas/templates/tableInformarValorLaudoRelatorioBenfeitorias.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];

                var TIPO_BENFEITORIA = 'TIPO_BENFEITORIA';

                var init = function () {
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                };

                vm.calcularValorTerreno = function () {
                    if (vm.tipoValorLaudo === 'INFORMAR_VALORES_LAUDO') {
                        if (vm.benfeitoriasImovel && vm.benfeitoriasImovel.length >= 1) {
                            vm.valorTerreno = vm.valorAdotadoSelecionado;
                            var valorTerreno = vm.valorTerreno;
                            for (i = 0; i < vm.benfeitoriasImovel.length; i++) {
                                valorTerreno -= vm.benfeitoriasImovel[i].benfeitoriaImovel.valor;
                                if(valorTerreno < 0){
                                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                                    vm.benfeitoriasImovel[i].benfeitoriaImovel.valor = 0;
                                    return;
                                }
                            }
                            vm.valorTerreno = valorTerreno;
                        }
                    }
                };

                var pesquisarPadroesAcabamento = function () {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function (resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function () {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function (resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                vm.goInformarValorCub = function(benfeitoria) {
                    vm.redirecionarInformarValorCub(benfeitoria, TIPO_BENFEITORIA, vm.tipoValorLaudo);
                };

                vm.visualizarEstimativasValor = function (ev, benfeitoria) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", "cubAvaliacaoService", function ($scope, cubAvaliacaoService) {
                            $scope.listaMotivosEstimativa = [];

                            $scope.benfeitoria = benfeitoria;
                            var init = function () {
                                pesquisarMotivosEstimativaValor();
                            };

                            var pesquisarMotivosEstimativaValor = function () {
                                cubAvaliacaoService.buscarMotivosEstimativaValor().then(function (resp) {
                                    $scope.listaMotivosEstimativa = resp.data;
                                });
                            };

                            $scope.fecharDialogDetalhesValorCub = function () {
                                $mdDialog.cancel();
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesCubBenfeitoria.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                init();
            }
        };
    }

})();
(function () {

    directive.$inject = ["comumAvaliacaoService", "$mdMedia", "$mdDialog", "$filter", "ComumFactory"];
    angular.module('su-avaliacao')
        .directive('tableInformarValorLaudoBenfeitorias', directive);

    function directive(comumAvaliacaoService, $mdMedia, $mdDialog, $filter, ComumFactory) {
        return {
            restrict: 'EA',
            scope: {
                benfeitoriasImovel: '=',
                detalharLaudoRelatorio: '=',
                nomeCampo: '=',
                redirecionarInformarValorCub: '=',
                valorAdotadoSelecionado: '=',
                tipoValorLaudo: '=',
                valorTerreno: '=',
                totalValorTerreno: '=',
                moeda: '=',
                listaMoedas: '=?',
                termoResponsabilidade: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/tabelas/templates/tableInformarValorLaudoBenfeitorias.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];

                var TIPO_BENFEITORIA = 'TIPO_BENFEITORIA';

                var init = function () {
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                };

                vm.calcularValorTerreno = function (benfeitoria) {
                    if (vm.tipoValorLaudo === 'INFORMAR_VALORES_LAUDO') {
                        var terreno =  vm.valorAdotadoSelecionado;
                        var erro = false
                        if (vm.benfeitoriasImovel && vm.benfeitoriasImovel.length >= 1) {
                            for (i = 0; i < vm.benfeitoriasImovel.length; i++) {
                                terreno -= vm.benfeitoriasImovel[i].valor ? vm.benfeitoriasImovel[i].valor:0;
                                if(terreno < 0){
                                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                                    erro = true
                                    benfeitoria.valor = 0;
                                    benfeitoria.valorCub = 0;
                                    if (benfeitoria.benfeitoriaImovel) {
                                        benfeitoria.benfeitoriaImovel.cubDepreciado = 0;
                                    }
                                    return;
                                }
                            }

                            if (!erro && vm.valorTerreno != terreno) {
                                if (vm.termoResponsabilidade) {
                                    vm.termoResponsabilidade.isAssinado = false;
                                }
                                vm.valorTerreno = terreno;
                            }
                        }
                    }
                };

                var pesquisarPadroesAcabamento = function () {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function (resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function () {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function (resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                vm.goInformarValorCub = function (benfeitoria) {
                    vm.redirecionarInformarValorCub(benfeitoria, TIPO_BENFEITORIA);
                };

                vm.visualizarEstimativasValor = function (ev, benfeitoria) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", "cubAvaliacaoService", function ($scope, cubAvaliacaoService) {
                            $scope.listaMotivosEstimativa = [];

                            $scope.benfeitoria = benfeitoria;
                            var init = function () {
                                pesquisarMotivosEstimativaValor();
                            };

                            var pesquisarMotivosEstimativaValor = function () {
                                cubAvaliacaoService.buscarMotivosEstimativaValor().then(function (resp) {
                                    $scope.listaMotivosEstimativa = resp.data;
                                });
                            };

                            $scope.fecharDialogDetalhesValorCub = function () {
                                $mdDialog.cancel();
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesCubBenfeitoria.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                vm.calcularCubDepreciado = function (benfeitoria) {
                    if (benfeitoria.valorCub && benfeitoria.areaConstruida) {
                        benfeitoria.valor = benfeitoria.valorCub * benfeitoria.areaConstruida;
                
                        benfeitoria.benfeitoriaImovel = benfeitoria.benfeitoriaImovel || {};
                        benfeitoria.benfeitoriaImovel.cubDepreciado = benfeitoria.valorCub;
                
                        vm.calcularValorTerreno(benfeitoria);
                    }
                };

                vm.limparCub = function (benfeitoria) {
                    benfeitoria.valorCub = 0;
                
                    if (benfeitoria.benfeitoriaImovel) {
                        benfeitoria.benfeitoriaImovel.cubDepreciado = 0;
                    }
                };

                vm.limparValorBenfeitoria = function (benfeitoria) {
                    benfeitoria.valor = 0;
                };
                
                init();
            }
        };
    }

})();

(function () {

    directive.$inject = ["comumAvaliacaoService", "$mdMedia", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('tableInformarValorEdificacao', directive);

    function directive (comumAvaliacaoService, $mdMedia, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                benfeitoriasImovel: '=',
                detalharLaudoRelatorio: '=',
                nomeCampo: '=',
                redirecionarInformarValorEdificacao: '=',
                valorAdotado: '=',
                tipoValorLaudo: '=',
                valorTerreno: '=',
                totalValorTerreno: '=',
                moeda: '=',
                listaMoedas: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/tabelas/templates/tableInformarValorEdificacao.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];

                var TIPO_BENFEITORIA = 'TIPO_BENFEITORIA';

                var init = function() {
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                };

                var pesquisarPadroesAcabamento = function() {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function(resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function() {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function(resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                vm.adicionarValor = function (benfeitoria) {
                    vm.redirecionarInformarValorEdificacao(benfeitoria, TIPO_BENFEITORIA);
                };

                vm.visualizarEstimativasValor = function(ev, benfeitoria) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", "cubAvaliacaoService", function ($scope, cubAvaliacaoService) {
                            $scope.listaMotivosEstimativa = [];

                            $scope.benfeitoria = benfeitoria;
                            var init = function () {
                                pesquisarMotivosEstimativaValor();
                            };

                            var pesquisarMotivosEstimativaValor = function () {
                                cubAvaliacaoService.buscarMotivosEstimativaValor().then(function (resp) {
                                    $scope.listaMotivosEstimativa = resp.data;
                                });
                            };

                            $scope.fecharDialogDetalhesValorCub = function () {
                                $mdDialog.cancel();
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesCubBenfeitoria.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["comumAvaliacaoService", "ComumFactory", "$filter"];
    angular.module('su-avaliacao')
        .directive('tableEstimarValorTerrenoLaudoUnidadeAutonoma', directive);

    function directive (comumAvaliacaoService, ComumFactory, $filter) {
        return {
            restrict: 'EA',
            scope: {
                unidadeAutonomaImovel: '=',
                detalharLaudoRelatorio: '=',
                nomeCampo: '=',
                redirecionarInformarValorCub: '=',
                tipoValorLaudo: '=',
                valorTerreno: '=',
                totalValorTerreno: '=',
                valorAdotadoSelecionado: '=',
                moeda: '=',
                listaMoedas: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/tabelas/templates/tableInformarValorLaudoUnidadeAutonoma.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];

                vm.TIPO_UNIDADE_AUTONOMA = 'TIPO_UNIDADE_AUTONOMA';

                var init = function() {
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                };

                var pesquisarPadroesAcabamento = function() {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function(resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function() {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function(resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                vm.goInformarValorCub = function() {
                    vm.redirecionarInformarValorCub(null, vm.TIPO_UNIDADE_AUTONOMA);
                };

                vm.calcularValorTerreno = function() {
                    if (vm.tipoValorLaudo === 'INFORMAR_VALORES_LAUDO') {
                        if (vm.unidadeAutonomaImovel && vm.unidadeAutonomaImovel.length === 1) {
                            vm.valorTerreno = vm.valorAdotadoSelecionado - vm.unidadeAutonomaImovel[0].valor;
                            vm.totalValorTerreno = vm.unidadeAutonomaImovel[0].valor;
                            if(vm.valorTerreno < 0){
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                                vm.unidadeAutonomaImovel[0].valor = 0;
                                return;
                            }
                        }
                    }
                };

                init();
            }
        };
    }

})();
(function () {

    directive.$inject = ["comumAvaliacaoService", "$mdMedia", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('tableEstimarValorTerrenoLaudoRelatorioBenfeitorias', directive);

    function directive (comumAvaliacaoService, $mdMedia, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                benfeitoriasImovel: '=',
                detalharLaudoRelatorio: '=',
                nomeCampo: '=',
                redirecionarInformarValorCub: '=',
                tipoValorLaudo: '=',
                moeda: '=',
                listaMoedas: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorTerrenoLaudoRelatorioBenfeitorias.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];

                var TIPO_BENFEITORIA = 'TIPO_BENFEITORIA';

                var init = function() {
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                };

                var pesquisarPadroesAcabamento = function() {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function(resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function() {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function(resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                vm.goInformarValorCub = function(benfeitoria) {
                    vm.redirecionarInformarValorCub(benfeitoria, TIPO_BENFEITORIA, vm.tipoValorLaudo);
                };

                vm.visualizarEstimativasValor = function(ev, benfeitoria) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", "cubAvaliacaoService", function ($scope, cubAvaliacaoService) {
                            $scope.listaMotivosEstimativa = [];

                            $scope.benfeitoria = benfeitoria;
                            var init = function () {
                                pesquisarMotivosEstimativaValor();
                            };

                            var pesquisarMotivosEstimativaValor = function() {
                                cubAvaliacaoService.buscarMotivosEstimativaValor().then(function(resp) {
                                    $scope.listaMotivosEstimativa = resp.data;
                                });
                            };

                            $scope.fecharDialogDetalhesValorCub = function() {
                                $mdDialog.cancel();
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesCubBenfeitoria.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["comumAvaliacaoService", "$mdMedia", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('tableEstimarValorTerrenoLaudoBenfeitorias', directive);

    function directive(comumAvaliacaoService, $mdMedia, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                benfeitoriasImovel: '=',
                detalharLaudoRelatorio: '=',
                nomeCampo: '=',
                redirecionarInformarValorCub: '=',
                tipoValorLaudo: '=',
                moeda: '=',
                listaMoedas: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorTerrenoLaudoBenfeitorias.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];

                var TIPO_BENFEITORIA = 'TIPO_BENFEITORIA';

                var init = function () {
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                };

                var pesquisarPadroesAcabamento = function () {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function (resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function () {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function (resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                vm.goInformarValorCub = function (benfeitoria) {
                    vm.redirecionarInformarValorCub(benfeitoria, TIPO_BENFEITORIA);
                };

                vm.visualizarEstimativasValor = function (ev, benfeitoria) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", "cubAvaliacaoService", function ($scope, cubAvaliacaoService) {
                            $scope.listaMotivosEstimativa = [];

                            $scope.benfeitoria = benfeitoria;
                            var init = function () {
                                pesquisarMotivosEstimativaValor();
                            };

                            var pesquisarMotivosEstimativaValor = function () {
                                cubAvaliacaoService.buscarMotivosEstimativaValor().then(function (resp) {
                                    $scope.listaMotivosEstimativa = resp.data;
                                });
                            };

                            $scope.fecharDialogDetalhesValorCub = function () {
                                $mdDialog.cancel();
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesCubBenfeitoria.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["comumAvaliacaoService", "$mdMedia", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('tableEstimarValorLaudoUnidadeAutonoma', directive);

    function directive (comumAvaliacaoService, $mdMedia, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                unidadeAutonomaImovel: '=',
                detalharLaudoRelatorio: '=',
                nomeCampo: '=',
                redirecionarInformarValorCub: '=',
                tipoValorLaudo: '=',
                moeda: '=',
                listaMoedas: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorLaudoUnidadeAutonoma.html',
            link: function ($scope) {

                var vm = $scope;

                vm.TIPO_UNIDADE_AUTONOMA = 'TIPO_UNIDADE_AUTONOMA';

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];

                var init = function() {
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                };

                var pesquisarPadroesAcabamento = function() {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function(resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function() {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function(resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                vm.goInformarValorCub = function() {
                    vm.redirecionarInformarValorCub(null, vm.TIPO_UNIDADE_AUTONOMA);
                };

                vm.visualizarEstimativasValor = function(ev, unidadeAutonoma) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", "cubAvaliacaoService", function ($scope, cubAvaliacaoService) {
                            $scope.listaMotivosEstimativa = [];

                            $scope.unidadeAutonoma = unidadeAutonoma;
                            var init = function () {
                                pesquisarMotivosEstimativaValor();
                            };

                            var pesquisarMotivosEstimativaValor = function() {
                                cubAvaliacaoService.buscarMotivosEstimativaValor().then(function(resp) {
                                    $scope.listaMotivosEstimativa = resp.data;
                                });
                            };

                            $scope.fecharDialogDetalhesValorCub = function() {
                                $mdDialog.cancel();
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesCubUnidadeAutonoma.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["comumAvaliacaoService", "$mdMedia", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('tableEstimarValorLaudoRelatorioBenfeitorias', directive);

    function directive (comumAvaliacaoService, $mdMedia, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                benfeitoriasImovel: '=',
                detalharLaudoRelatorio: '=',
                nomeCampo: '=',
                redirecionarInformarValorCub: '=',
                tipoValorLaudo: '=',
                moeda: '=',
                listaMoedas: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorLaudoRelatorioBenfeitorias.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];

                var TIPO_BENFEITORIA = 'TIPO_BENFEITORIA';

                var init = function() {
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                };

                var pesquisarPadroesAcabamento = function() {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function(resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function() {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function(resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                vm.goInformarValorCub = function(benfeitoria) {
                    vm.redirecionarInformarValorCub(benfeitoria, TIPO_BENFEITORIA, vm.tipoValorLaudo);
                };

                vm.visualizarEstimativasValor = function(ev, benfeitoria) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", "cubAvaliacaoService", function ($scope, cubAvaliacaoService) {
                            $scope.listaMotivosEstimativa = [];

                            $scope.benfeitoria = benfeitoria;
                            var init = function () {
                                pesquisarMotivosEstimativaValor();
                            };

                            var pesquisarMotivosEstimativaValor = function() {
                                cubAvaliacaoService.buscarMotivosEstimativaValor().then(function(resp) {
                                    $scope.listaMotivosEstimativa = resp.data;
                                });
                            };

                            $scope.fecharDialogDetalhesValorCub = function() {
                                $mdDialog.cancel();
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesCubBenfeitoria.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["comumAvaliacaoService", "$mdMedia", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('tableEstimarValorLaudoBenfeitorias', directive);

    function directive(comumAvaliacaoService, $mdMedia, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                benfeitoriasImovel: '=',
                detalharLaudoRelatorio: '=',
                nomeCampo: '=',
                redirecionarInformarValorCub: '=',
                tipoValorLaudo: '=',
                moeda: '=',
                listaMoedas: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorLaudoBenfeitorias.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaPadraoAcabamento = [];
                vm.listaTiposProjeto = [];

                var TIPO_BENFEITORIA = 'TIPO_BENFEITORIA';

                var init = function () {
                    pesquisarPadroesAcabamento();
                    pesquisarTiposProjetoCub();
                };

                var pesquisarPadroesAcabamento = function () {
                    comumAvaliacaoService.buscarPadroesAcabamento().then(function (resp) {
                        vm.listaPadraoAcabamento = resp.data;
                    });
                };

                var pesquisarTiposProjetoCub = function () {
                    comumAvaliacaoService.buscarTiposProjetosCub().then(function (resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                vm.goInformarValorCub = function (benfeitoria) {
                    vm.redirecionarInformarValorCub(benfeitoria, TIPO_BENFEITORIA);
                };

                vm.visualizarEstimativasValor = function (ev, benfeitoria) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", "cubAvaliacaoService", function ($scope, cubAvaliacaoService) {
                            $scope.listaMotivosEstimativa = [];

                            $scope.benfeitoria = benfeitoria;
                            var init = function () {
                                pesquisarMotivosEstimativaValor();
                            };

                            var pesquisarMotivosEstimativaValor = function () {
                                cubAvaliacaoService.buscarMotivosEstimativaValor().then(function (resp) {
                                    $scope.listaMotivosEstimativa = resp.data;
                                });
                            };

                            $scope.fecharDialogDetalhesValorCub = function () {
                                $mdDialog.cancel();
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesCubBenfeitoria.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["$mdDialog", "ComumFactory", "$filter", "$state", "$timeout", "avaliacaoLaudoService", "amostraService", "imovelAvaliacaoService", "ciMapaAvaliacaoLayersFactory", "ciMapaAvaliacaoViewFactory", "NotificadorService", "MAPA_CONSTANTS"];
    angular.module('su-avaliacao')
        .directive('variavelAmostra', directive);

    function directive($mdDialog, ComumFactory, $filter, $state, $timeout, avaliacaoLaudoService, amostraService, imovelAvaliacaoService, ciMapaAvaliacaoLayersFactory, ciMapaAvaliacaoViewFactory, NotificadorService, MAPA_CONSTANTS) {
        return {
            restrict: 'EA',
            scope: {
                avaliacao: '=',
                variaveis: '=',
                amostras: '=',
                detalharLaudoRelatorio: '=',
                redirecionarSelecionarAmostras: '=',
                redirecionarAdicionarVariavelAmostras: '=',
                salvarComoRascunhoLaudoRelatorio: '=',
                tipoLaudo: '=',
                isBenfeitoriaEdificacao: '=',
                isBenfeitoriaSemEdificacao: '=',
                isUnidadeAutonoma: '=',
                variaveisAmostras: '=',
                rip: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/variaveisAmostrasLaudoRelatorio.tmpl.html',
            link: function ($scope) {

                var vm = $scope;
                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;
                vm.paginationVariaveis = [];

                vm.editar = {};
                vm.editar.buttonShow = false;

                ciMapaAvaliacaoLayersFactory.init();
                ciMapaAvaliacaoViewFactory.init();

                vm.listaCondicoes = {SIM: true, NAO: false};
                vm.listaNomesVariavelCompleta = {
                    AREA_TERRENO: "Área do terreno",
                    AREA_CONSTRUIDA: "Área Construída",
                    PADRAO_ACABAMENTO: "Padrão de Acabamento",
                    ESTADO_CONSERVACAO: "Estado e Conservação",
                    ELEVADOR: "Elevador",
                    GARAGEM: "Garagem",
                    ESQUINA: "Esquina",
                    POTENCIAL_CONSTRUTIVO: "Potencial Construtivo",
                    TESTADA: "Testada",
                    TOPOGRAFIA: "Topografia",
                    VALOR_TOTAL: "Valor Total",
                    VALOR_UNITARIO: "Valor Unitário",
                    VALOR_TOTAL_LOCACAO: "Valor Total de Locação",
                    VALOR_UNITARIO_LOCACAO: "Valor Unitário de Locação",
                    OUTRO: "Outro"
                };

                vm.colorTableAmostras = {
                    'background-color': '#ffcccc'
                };
                vm.mostrarAviso = false;
                vm.carregarMapa = false;

                vm.$watch('amostras', function () {
                    loadMapas(function () {
                        if (vm.amostras && vm.amostras.length > 0) {
                            vm.carregarMapa = true;
                            $timeout(function () {
                                marcarPontosMapa(vm.amostras);
                            },1000);


                        }
                    });

                });

                vm.$watch('rip', function () {
                    if (vm.rip) {
                        imovelAvaliacaoService.consultarImovelPorRip(vm.rip).then(function (resposta) {
                            vm.imovel = resposta.data;
                            if (vm.imovel) {
                                imovelAvaliacaoService.buscarBenfeitoriasSuPorRip(vm.rip).then(function (resp) {
                                    vm.imovel.benfeitorias = resp.data;
                                });

                            }
                        });
                    }

                });

                function loadMapas(callback) {

                    ciMapaAvaliacaoLayersFactory.init();
                    ciMapaAvaliacaoViewFactory.init();
                    if (callback) {
                        $timeout(function () {
                            callback();
                        });
                    }
                };

                var init = function () {
                    pesquisarTiposVariaveis();
                    pesquisarVariaveisEnum();


                };

                var marcarPontosMapa = function (amostras) {

                    var variaveisAmostras = {};
                    variaveisAmostras.amostras = [];
                    variaveisAmostras.imovel = {};
                    variaveisAmostras.amostras = angular.copy(amostras);
                    variaveisAmostras.imovel = angular.copy(vm.imovel);

                    NotificadorService.notify(vm, variaveisAmostras, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_PONTOS_AVALIACAO_TERRENO);
                };


                vm.incluirAmostra = function () {
                    vm.redirecionarAdicionarVariavelAmostras(vm.avaliacao)
                };

                vm.selecionarAmostra = function () {
                    vm.redirecionarSelecionarAmostras(vm.avaliacao);
                };

                vm.adicionarVariavel = function () {
                    if (vm.variavel.nome && vm.variavel.variavel && vm.variavel.tipoVariavel && vm.variavel.descricao && vm.variavel.isVariavelUtilizadaModelo != null && ((vm.variavel.nome === 'OUTRO' && vm.variavel.nomeOutraVariavel) || vm.variavel.nome !== 'OUTRO')) {
                        vm.variavel.avaliacao = {};
                        if (!vm.avaliacao.id) {
                            vm.salvarComoRascunhoLaudoRelatorio();
                            return;
                        }
                        if (vm.variavel.nome === 'OUTRO') {
                            for (var i = 0; i < vm.variaveis.length; i++) {
                                if (vm.variaveis[i].nomeOutraVariavel === vm.variavel.nomeOutraVariavel) {
                                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-variavel-nome-informada-ja-foi-adicionada'));
                                    return;
                                }
                            }
                        }
                        vm.variavel.avaliacao.id = vm.avaliacao.id;
                        avaliacaoLaudoService.salvarVariavel(vm.variavel).then(function (resp) {
                            vm.variavel = resp.data;
                            vm.variaveis.push(vm.variavel);
                            vm.variavel = {};
                            vm.paginacaoVariaveis();
                            vm.listaNomesVariavel = {};
                            vm.carregarMapa = true;
                            $timeout(function () {
                                marcarPontosMapa(vm.amostras);
                            },1000);
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-variavel-adicionada-sucesso'));
                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        vm.variavel.nome.$invalid = true;
                        vm.variavel.nomeOutraVariavel.$invalid = true;
                        vm.variavel.descricao.$invalid = true;
                        vm.variavel.tipoVariavel.$invalid = true;
                        vm.variavel.variavel.$invalid = true;
                    }
                };


                vm.alterarVariavel = function ($index) {
                    vm.variavel = angular.copy(vm.paginationVariaveis[$index]);
                    vm.editar.index = $index;
                    vm.editar.buttonShow = true;
                };

                vm.gravarAlteracaoVariavel = function () {

                    if (vm.variavel.variavel && vm.variavel.tipoVariavel && vm.variavel.nome && vm.variavel.descricao && vm.variavel.isVariavelUtilizadaModelo != null && ((vm.variavel.nome === 'OUTRO' && vm.variavel.nomeOutraVariavel) || vm.variavel.nome !== 'OUTRO')) {
                        if (vm.variavel.nome === 'OUTRO') {
                            for (var i = 0; i < vm.variaveis.length; i++) {
                                if (vm.variaveis[i].nomeOutraVariavel === vm.variavel.nomeOutraVariavel && vm.variaveis[i].id !== vm.variavel.id) {
                                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-variavel-nome-informada-ja-foi-adicionada'));
                                    return;
                                }
                            }
                        }

                        avaliacaoLaudoService.salvarVariavel(vm.variavel).then(function (resp) {
                            vm.variavel = resp.data;
                            vm.variaveis.splice(vm.editar.index, 1);
                            vm.variaveis.push(vm.variavel);
                            vm.variavel = {};
                            vm.paginacaoVariaveis();
                            vm.editar.index = null;
                            vm.editar.buttonShow = false;
                            vm.listaNomesVariavel = {};
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-variavel-alterada-sucesso'));
                        });

                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        vm.variavel.nome.$invalid = true;
                        vm.variavel.descricao.$invalid = true;
                        vm.variavel.tipoVariavel.$invalid = true;
                        vm.variavel.variavel.$invalid = true;
                    }
                };


                vm.removerVariavel = function ($index, id) {
                    if(vm.paginationAmostras.length > 0) {
                        var confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent($filter('translate')('mensagem-remover-esta-variavel-com-dados-mercado-confirm'))
                            .ok("Confirmar")
                            .cancel("Cancelar");
                    }else{
                        var confirm = $mdDialog.confirm()
                            .title("Atenção!")
                            .textContent($filter('translate')('mensagem-remover-esta-variavel-confirm'))
                            .ok("Confirmar")
                            .cancel("Cancelar");
                    }

                    $mdDialog.show(confirm).then(function () {
                        avaliacaoLaudoService.excluirVariavel(id).then(function () {
                            vm.variaveis.splice($index, 1);
                            vm.paginacaoVariaveis();
                            buscarAmostras();
                        });
                    });
                };

                vm.removerAmostra = function ($index, id) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent($filter('translate')('mensagem-remover-dado-mercado'))
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        avaliacaoLaudoService.excluirVariavelAmostra(id).then(function () {
                            vm.amostras.splice($index, 1);
                            buscarAmostras();
                        });
                    });
                };

                vm.$watch('variaveis', function () {
                    if (!vm.variaveis) {
                        vm.variaveis = [];
                    }
                    if (!vm.amostras) {
                        vm.amostras = [];
                    }
                    buscarAmostras();
                    vm.paginacaoVariaveis();
                });

                vm.paginacaoVariaveis = function () {
                    vm.paginationVariaveis = [];
                    vm.totalItems = vm.variaveis.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationVariaveis = vm.variaveis;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationVariaveis.push(vm.variaveis[i]);
                            if (vm.paginationVariaveis.length === vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                var pesquisarTiposVariaveis = function () {
                    avaliacaoLaudoService.buscarTiposVariavel().then(function (resp) {
                        vm.listaTiposVariavel = resp.data;
                    });
                };

                var pesquisarVariaveisEnum = function () {
                    avaliacaoLaudoService.buscarVariaveisEnum().then(function (resp) {
                        vm.listaVariaveis = resp.data;
                    });
                };

                vm.paginacaoAmostras = function () {
                    vm.paginationAmostras = [];
                    vm.totalItems = vm.amostras.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationAmostras = vm.amostras;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationAmostras.push(vm.amostras[i]);
                            if (vm.paginationAmostras.length === vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                vm.showDialogDescricaoVariavel = function (ev, variavelTemp) {
                    $mdDialog.show({
                        controller: function () {
                            vm.variavelModal = variavelTemp;

                            vm.cancelModal = function () {
                                vm.variavelModal = null;
                                $mdDialog.cancel();
                            };

                            return vm;
                        },
                        controllerAs: 'vm',
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDetalhesDescricaoVariavel.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: true
                    });
                };


                vm.editarAmostra = function (idAmostra) {
                    var variaveisAmostras = [];
                    for (var i = 0; i < vm.variaveisAmostras.length; i++) {
                        if (vm.variaveisAmostras[i].amostra.id === idAmostra) {
                            variaveisAmostras.push(vm.variaveisAmostras[i]);
                        }
                    }
                    vm.redirecionarAdicionarVariavelAmostras(vm.avaliacao, variaveisAmostras, idAmostra)
                };

                var buscarAmostras = function () {
                    if (vm.avaliacao.id) {
                        avaliacaoLaudoService.buscarAmostras(vm.avaliacao.id).then(function (resp) {
                            vm.variaveisAmostras = resp.data;
                            vm.amostras = [];
                            var j = 0;
                            if (vm.variaveisAmostras && vm.variaveisAmostras.length > j) {
                                vm.amostras.push(vm.variaveisAmostras[j].amostra);

                            }
                            for (var i = 0; i < vm.variaveisAmostras.length; i++) {
                                if (!vm.variaveisAmostras[i].valor) {
                                    vm.mostrarAviso = true;
                                }
                                if (vm.variaveisAmostras[i].amostra.id !== vm.amostras[j].id) {
                                    vm.amostras.push(vm.variaveisAmostras[i].amostra);
                                    j++;
                                }
                            }
                            vm.paginacaoAmostras();
                            loadMapas(function () {
                                if (vm.amostras && vm.amostras.length > 0) {
                                    vm.carregarMapa = true;
                                    $timeout(function () {
                                        marcarPontosMapa(vm.amostras);
                                    },1000)

                                }
                            });
                        });
                    }
                };

                vm.changeValorVariavel = function () {
                    vm.mostrarAviso = false;
                    for (var i = 0; i < vm.variaveisAmostras.length; i++) {
                        if (!vm.variaveisAmostras[i].valor) {
                            vm.mostrarAviso = true;
                        }
                    }
                };

                vm.changeUp = function (id) {
                    if (vm.amostras.length > 1) {
                        for (i++; i < vm.amostras.length; i++) {
                            if (vm.amostras[i].id === id) {
                                vm.amostras[i].ordem = vm.amostras[i].ordem - 1;
                                vm.amostras[i - 1].ordem = vm.amostras[i - 1].ordem + 1;
                                amostraService.salvar(vm.amostras[i]).then(function () {
                                    amostraService.salvar(vm.amostras[i - 1]).then(function () {
                                        buscarAmostras();
                                        return;
                                    });
                                });


                            }
                        }
                    }
                };

                vm.changeDown = function (id) {
                    if (vm.amostras.length > 1) {
                        for (i++; i < vm.amostras.length; i++) {
                            if (vm.amostras[i].id === id) {
                                vm.amostras[i].ordem = vm.amostras[i].ordem + 1;
                                vm.amostras[i + 1].ordem = vm.amostras[i + 1].ordem - 1;
                                amostraService.salvar(vm.amostras[i]).then(function () {
                                    amostraService.salvar(vm.amostras[i + 1]).then(function () {
                                        buscarAmostras();
                                        return;
                                    });
                                });
                            }
                        }
                    }
                };

                vm.gerarTabelaVariaveis = function (variaveis) {
                    var lista = "";
                    var tipo = "";
                    for (var i = 0; i < variaveis.length; i++) {
                        if (i !== 0) {
                            lista += ",";
                        }
                        lista += variaveis[i].nome === 'OUTRO' ? variaveis[i].nomeOutraVariavel : vm.listaNomesVariavelCompleta[variaveis[i].nome];

                    }
                    if (vm.isBenfeitoriaEdificacao) {
                        tipo = "BENFEITORIA_EDIFICACAO";
                    } else if (vm.isBenfeitoriaSemEdificacao) {
                        tipo = "BENFEITORIA_SEM_EDIFICACAO";
                    } else if (vm.isUnidadeAutonoma) {
                        tipo = "UNIDADE_AUTONOMA";
                    }
                    var a = document.createElement("a");
                    a.setAttribute("href", '/avaliacao/api/avaliacao/gerar-tabela-variaveis/' + lista + "/" + vm.avaliacao.id + "/" + tipo);
                    a.setAttribute("target", "_blank");
                    var clickEvent = new MouseEvent("click", {
                        "view": window,
                        "bubbles": true,
                        "cancelable": false
                    });
                    a.dispatchEvent(clickEvent);

                };

                vm.changeVariavel = function () {
                    if (vm.variavel.variavel === 'DEPENDENTE') {
                        if (vm.tipoLaudo === 'LAUDO_LOCACAO_ARRENDAMENTO') {
                            vm.listaNomesVariavel = {
                                VALOR_TOTAL_LOCACAO: "Valor Total de Locação",
                                VALOR_UNITARIO_LOCACAO: "Valor Unitário de Locação",
                                OUTRO: "Outro"
                            }
                        } else {
                            vm.listaNomesVariavel = {
                                VALOR_TOTAL: "Valor Total",
                                VALOR_UNITARIO: "Valor Unitário",
                                OUTRO: "Outro"
                            }
                        }
                    } else if (vm.variavel.variavel === 'INDEPENDENTE') {
                        if (vm.isBenfeitoriaEdificacao) {
                            vm.listaNomesVariavel = {
                                AREA_TERRENO: "Área do terreno",
                                AREA_CONSTRUIDA: "Área Construída",
                                PADRAO_ACABAMENTO: "Padrão de Acabamento",
                                ESTADO_CONSERVACAO: "Estado e Conservação",
                                GARAGEM: "Garagem",
                                OUTRO: "Outro"
                            }
                        } else if (vm.isBenfeitoriaSemEdificacao) {
                            vm.listaNomesVariavel = {
                                AREA_TERRENO: "Área do terreno",
                                ESQUINA: "Esquina",
                                POTENCIAL_CONSTRUTIVO: "Potencial Construtivo",
                                TESTADA: "Testada",
                                TOPOGRAFIA: "Topografia",
                                OUTRO: "Outro"
                            }
                        } else if (vm.isUnidadeAutonoma) {
                            vm.listaNomesVariavel = {
                                AREA_TERRENO: "Área do terreno",
                                AREA_CONSTRUIDA: "Área Construída",
                                PADRAO_ACABAMENTO: "Padrão de Acabamento",
                                ESTADO_CONSERVACAO: "Estado e Conservação",
                                ELEVADOR: "Elevador",
                                GARAGEM: "Garagem",
                                OUTRO: "Outro"
                            }
                        }
                    }

                    if (vm.variaveis) {
                        for (var i = 0; i < vm.variaveis.length; i++) {
                            if (vm.variaveis[i].nome !== 'OUTRO') {
                                delete vm.listaNomesVariavel[vm.variaveis[i].nome];
                            }
                        }
                    }
                };

                vm.importarAmostra = function (file) {
                    var content_type = ['application/vnd.ms-excel'];
                    if (content_type.indexOf(file[0].type) !== -1) {
                        avaliacaoLaudoService.importarAmostra(file[0], vm.avaliacao.id).then(function () {
                            buscarAmostras();
                        }, function () {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-formato-incompativel-modelo-variaveis'));
                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-formato-incompativel-modelo-variaveis'));
                    }
                };

                vm.getAmostraUtilizada = function(variaveis, amostraId) {
                    if(variaveis) {
                        return variaveis.filter(function(variavel) {return variavel.amostra.id === amostraId})[0].isAmostraUtilizada ? 'Sim' : 'Não';
                    }
                };

                vm.getColorLinha = function(variaveis, amostraId) {
                    if(variaveis) {
                        return variaveis.filter(function(variavel){return variavel.amostra.id === amostraId && variavel.valor == null}).length > 0;
                    }
                };

                init();
            }
        }
    }

})();

(function () {

    directive.$inject = ["usuarioAvaliacaoService", "comumAvaliacaoService", "ComumFactory", "$filter", "$mdDialog", "$state", "$timeout"];
    angular.module('su-avaliacao')
        .directive('responsavelTecnico', directive);

    function directive(usuarioAvaliacaoService, comumAvaliacaoService, ComumFactory, $filter, $mdDialog, $state, $timeout) {
        return {
            restrict: 'EA',
            scope: {
                listaResponsaveisTecnicos: '=',
                detalharLaudoRelatorio: '=',
                customTitle: '=?',
                incluirUsuarioLogado: '=?',
                descricaoHint: '=?',
                assinaturaNotaTecnica: '=?',
                assinaturaRevalidacao: '=?',
                assinaturaLaudoRelatorio: '=?',
                modo: '=?'
            },
            templateUrl: function(elem, attrs){
                if (attrs.modo === 'compacto') {
                    return 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/responsavelTecnico.tmpl.view.html';
                }else{
                    return 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/responsavelTecnico.tmpl.html';
                }
            },
            link: function ($scope) {
                var vm = $scope;

                vm.nomeCampo = 'Identificação';
                vm.exibirHint = !!vm.descricaoHint;

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;
                vm.paginationResponsaveisTecnico = [];

                vm.editar = {};
                vm.editar.buttonShow = false;
                vm.responsavel = {};
                vm.pessoa = {};

                vm.labelVermelho = {color: 'red'};

                vm.incluirUsuarioLogado = angular.isDefined(vm.incluirUsuarioLogado) ? vm.incluirUsuarioLogado : true;

                vm.listaCreaCrau = {CREA: 'CREA', CAU: "CAU"};

                var init = function () {
                    if (vm.incluirUsuarioLogado && !vm.detalharLaudoRelatorio) {
                        $timeout(function () {
                            buscarUsuarioLogado();
                        }, 8000);
                    }
                };

                var buscarUsuarioLogado = function () {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        var usuarioLogado = resp.data;
                        if (!vm.listaResponsaveisTecnicos) {
                            vm.listaResponsaveisTecnicos = [];
                        }

                        if (vm.listaResponsaveisTecnicos.length === 0) {
                            vm.responsavel.cpf = usuarioLogado.cpf;
                            vm.buscarPessoaFisica(function () {
                                vm.listaResponsaveisTecnicos.push({
                                    nome: vm.pessoa.nome,
                                    crea: vm.responsavel.crea,
                                    registroProfissional: vm.responsavel.registroProfissional,
                                    email: usuarioLogado.email,
                                    cpf: usuarioLogado.cpf,
                                    usuarioLogado: true
                                });

                                vm.pessoa = {};
                                vm.responsavel = {};
                            });
                        }

                        vm.pessoa = {};
                        vm.responsavel = {};
                        vm.paginacaoResponsaveisTecnicos();
                    });
                };

                vm.buscarPessoaFisica = function (callback) {
                    comumAvaliacaoService.buscarPessoaFisica(vm.responsavel.cpf).then(function (resp) {
                        var pessoa = resp.data;
                        if (pessoa) {
                            vm.pessoa = pessoa;
                            vm.pessoa.endereco = angular.copy(pessoa.endereco);
                            vm.responsavel.crea = vm.pessoa.crea ? vm.pessoa.crea : (vm.pessoa.cau ? vm.pessoa.cau : null);
                            vm.responsavel.registroProfissional = vm.pessoa.crea ? 'CREA' : (vm.pessoa.cau ? 'CAU' : null);

                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-responsavel-tecnico-invalido'));
                            vm.responsavel.cpf = '';
                            vm.pessoa = null;
                        }

                        if (callback) {
                            callback();
                        }
                    }, function (error) {
                        // Tratamento específico para usuário sem permissão de acesso a serviços por permissão no acessos.
                        if (error.status === 422) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-usuario-nao-possui-permissao-acessar-servico-consulta-pessoa'));
                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-responsavel-tecnico-invalido'));
                        }
                        vm.responsavel = {};
                        vm.pessoa = null;
                    });
                };

                vm.changeRegistroProfissional = function () {
                    vm.responsavel.crea = vm.responsavel.registroProfissional === 'CREA' ? vm.pessoa.crea : vm.pessoa.cau;
                };

                var verificarCpfUsuarioSistema = function () {
                    if (vm.responsavel.cpf && vm.responsavel.cpf.length === 11) {
                        if (vm.assinaturaLaudoRelatorio) {
                            usuarioAvaliacaoService.getUsuarioSistema(vm.responsavel.cpf).then(function (resp) {
                                if (resp.data === "") {
                                    vm.responsavel.cpf = null;
                                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-responsavel-tecnico-nao-usuario-sistema'));
                                } else {
                                    vm.responsavel.email = resp.data.email;
                                    vm.buscarPessoaFisica();
                                }
                            });

                        } else {
                            vm.buscarPessoaFisica();
                        }
                    }
                };


                vm.detalharPessoa = function (pessoa) {
                    window.sessionStorage.setItem('dadoPessoaFisica', JSON.stringify(pessoa));
                    var rota = $state.href('avaliacao.DADOS_PESSOAS');
                    window.open(rota, '_blank');
                };

                vm.verificarCpfAoDigitar = function() {
                    var cpfNumeros = (vm.responsavel.cpf || "").replace(/\D/g, "");
                    if (cpfNumeros.length === 11) {
                        verificarCpfUsuarioSistema();
                    } else {
                        vm.responsavel = {};
                        vm.pessoa = null;
                    }
                };

                vm.adicionarResponsavelTecnico = function () {
                    if (vm.responsavel.cpf && vm.responsavel.crea && vm.responsavel.registroProfissional) {
                        if (!verificarCpfJaExiste(vm.responsavel.cpf)) {
                            vm.listaResponsaveisTecnicos.push({
                                cpf: vm.responsavel.cpf,
                                registroProfissional: vm.responsavel.registroProfissional,
                                crea: vm.responsavel.crea,
                                nome: (vm.pessoa.nome),
                                email: (vm.responsavel.email ? vm.responsavel.email : 'teste@teste.com'),
                                usuarioLogado: (vm.responsavel.usuarioLogado ? vm.responsavel.usuarioLogado : false)
                            });
                            if (vm.responsavel.registroProfissional === 'CREA' && vm.responsavel.crea !== vm.pessoa.crea) {
                                vm.pessoa.crea = vm.responsavel.crea;
                                atualizarPessoaFisica(vm.pessoa);
                            } else if ((vm.responsavel.registroProfissional === 'CAU' && vm.responsavel.crea !== vm.pessoa.cau)) {
                                vm.pessoa.cau = vm.responsavel.crea;
                                atualizarPessoaFisica(vm.pessoa);
                            }
                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-responsavel-tecnico-existe'));
                        }
                        vm.responsavel = {};
                        vm.pessoa = {};
                        vm.paginacaoResponsaveisTecnicos();
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        vm.responsavel.cpf.$invalid = true;
                        vm.responsavel.registroProfissional.$invalid = true;
                        vm.responsavel.crea.$invalid = true;
                    }
                };

                var verificarCpfJaExiste = function (cpf) {
                    var cpfExiste = false;
                    angular.forEach(vm.listaResponsaveisTecnicos, function (responsavelTecnico) {
                        if (responsavelTecnico.cpf === cpf) {
                            cpfExiste = true;
                        }
                    });
                    return cpfExiste;
                };

                var atualizarPessoaFisica = function (pessoa) {
                    comumAvaliacaoService.atualizarPessoaFisica(vm.pessoa).then(
                        function () {},
                        function (error) {
                            // Tratamento específico para usuário sem permissão de acesso a serviços por permissão no acessos.
                            if (error.status === 422) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-usuario-nao-possui-permissao-acessar-servico-consulta-pessoa'));
                            }
                            vm.responsavel = {};
                            vm.pessoa = null;
                        }
                    );
                }


                vm.alterarResponsavelTecnico = function ($index) {
                    vm.responsavel = angular.copy(vm.paginationResponsaveisTecnico[$index]);
                    vm.buscarPessoaFisica();
                    vm.editar.index = $index;
                    vm.editar.buttonShow = true;
                };

                vm.gravarAlteracaoResponsavelTecnico = function () {

                    if (vm.responsavel.cpf && vm.responsavel.crea && vm.responsavel.registroProfissional) {
                        vm.listaResponsaveisTecnicos.splice(vm.editar.index, 1);
                        if (!verificarCpfJaExiste(vm.responsavel.cpf)) {
                            vm.listaResponsaveisTecnicos.push({
                                cpf: vm.responsavel.cpf,
                                registroProfissional: vm.responsavel.registroProfissional,
                                crea: vm.responsavel.crea,
                                nome: (vm.pessoa.nome),
                                email: (vm.responsavel.email ? vm.responsavel.email : 'teste@teste.com'),
                                isAssinado: vm.responsavel.isAssinado,
                                dataAssinatura: vm.responsavel.dataAssinatura,
                                usuarioLogado: (vm.responsavel.usuarioLogado ? vm.responsavel.usuarioLogado : false)
                            });
                            if (vm.responsavel.registroProfissional === 'CREA' && vm.responsavel.crea !== vm.pessoa.crea) {
                                vm.pessoa.crea = vm.responsavel.crea;
                                atualizarPessoaFisica(vm.pessoa);
                            } else if ((vm.responsavel.registroProfissional === 'CAU' && vm.responsavel.crea !== vm.pessoa.cau)) {
                                vm.pessoa.cau = vm.responsavel.crea;
                                atualizarPessoaFisica(vm.pessoa);
                            }
                        }
                        vm.responsavel = {};
                        vm.pessoa = {};
                        vm.paginacaoResponsaveisTecnicos();
                        vm.editar.index = null;
                        vm.editar.buttonShow = false;

                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        vm.responsavel.cpf.$invalid = true;
                        vm.responsavel.registroProfissional.$invalid = true;
                        vm.responsavel.crea.$invalid = true;
                    }
                };


                vm.removerResponsavelTecnico = function ($index) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent($filter('translate')('mensagem-remover-responsavel-tecnico-confirm'))
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        vm.listaResponsaveisTecnicos.splice($index, 1);
                        vm.paginacaoResponsaveisTecnicos();
                    });
                };

                vm.$watch('listaResponsaveisTecnicos', function () {
                    if (!vm.listaResponsaveisTecnicos) {
                        vm.listaResponsaveisTecnicos = [];
                    }
                    vm.paginacaoResponsaveisTecnicos();
                });

                vm.paginacaoResponsaveisTecnicos = function () {
                    vm.paginationResponsaveisTecnico = [];
                    vm.totalItems = vm.listaResponsaveisTecnicos.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationResponsaveisTecnico = vm.listaResponsaveisTecnicos;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationResponsaveisTecnico.push(vm.listaResponsaveisTecnicos[i]);
                            if (vm.paginationResponsaveisTecnico.length === vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["dominioAvaliacaoService", "formularioAvaliacaoService", "$filter", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('matriculaTranscricao', directive);

    function directive(dominioAvaliacaoService, formularioAvaliacaoService, $filter, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                imovel: '=',
                detalharLaudoRelatorio: '=',
                listaMatriculasTranscricao: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/matriculaTranscricao.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.matriculaTranscricao = {};

                vm.listaUFs = [];
                vm.listaMunicipios = [];
                vm.listaCartorios = [];

                vm.paginationMatriculaTranscricao = [];

                vm.BRASIL = 'Brasil';
                vm.isMatricula = false;
                vm.isMostrarBotaoMatricula = true;
                vm.editar = {};
                vm.editar.buttonShow = false;

                var init = function () {
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;

                    pesquisarUfs();
                    vm.paginacaoMatriculasTranscricao();
                };

                var pesquisarUfs = function () {
                    dominioAvaliacaoService.pesquisarUfsIntegrador().then(function (respostaUf) {
                        vm.listaUFs = respostaUf.data;
                        if (vm.imovel.enderecamento) {
                            for (var i = 0; i < vm.listaUFs.length; i++) {
                                if (vm.imovel.enderecamento.uf === vm.listaUFs[i].sigla) {
                                    vm.matriculaTranscricao.uf = vm.listaUFs[i].sigla;
                                    vm.pesquisarMunicipiosPorUf();
                                    break;
                                }
                            }
                        }
                    });
                };

                vm.pesquisarMunicipiosPorUf = function () {
                    if (vm.matriculaTranscricao.uf) {
                        dominioAvaliacaoService.pesquisarMuniciposPorUfIntegrador(vm.matriculaTranscricao.uf).then(function (resposta) {
                            vm.listaMunicipios = resposta.data;
                            for (var i = 0; i < vm.listaMunicipios.length; i++) {
                                if (vm.imovel.enderecamento.municipio === vm.listaMunicipios[i].nome) {
                                    vm.matriculaTranscricao.municipio = vm.listaMunicipios[i];
                                    break;
                                }
                            }
                        });
                    }
                };

                vm.pesquisarCartoriosPorUfIntegrador = function () {
                    dominioAvaliacaoService.pesquisarCartoriosPorUfIntegrador(vm.matriculaTranscricao.uf)
                        .then(function (resp) {
                            vm.listaCartorios = resp.data;
                        });
                };

                vm.pesquisarCartoriosPorUfMunicipioIntegrador = function () {
                    if (vm.matriculaTranscricao.uf) {
                        dominioAvaliacaoService.pesquisarCartoriosPorUfMunicipioIntegrador(vm.matriculaTranscricao.uf, vm.matriculaTranscricao.municipio.codigo)
                            .then(function (resp) {
                                vm.listaCartorios = resp.data;
                            });
                    }
                };

                vm.inserirNovaMatriculaTranscricao = function () {
                    vm.isMatricula = true;
                    vm.isMostrarBotaoMatricula = false;
                };

                vm.alterarMatriculaTranscricao = function ($index) {
                    vm.matriculaTranscricao = angular.copy(vm.paginationMatriculaTranscricao[$index]);
                    vm.editar.index = $index;
                    vm.editar.buttonShow = true;
                    vm.isMatricula = true;
                    vm.isMostrarBotaoMatricula = false;
                };

                vm.gravarAlteracaoMatriculaTranscricao = function (formularioMatriculas) {
                    var confirm = $mdDialog.confirm({focusOnOpen: false})
                        .title($filter('translate')('label-titulo-confirmacao'))
                        .textContent($filter('translate')('mensagem-inclusao-registro-aplicada-cadastro-imovel-confirm'))
                        .ok($filter('translate')('btn-sim'))
                        .cancel($filter('translate')('btn-nao'));
                    $mdDialog.show(confirm).then(function () {
                        vm.validarFormulario(formularioMatriculas);

                        if (!formularioMatriculas.$valid) {
                            return;
                        }
                        vm.listaMatriculasTranscricao.splice(vm.editar.index, 1);
                        vm.listaMatriculasTranscricao.push({
                            id: null,
                            uf: vm.matriculaTranscricao.uf,
                            municipio: vm.matriculaTranscricao.municipio.nome,
                            cartorioID: vm.matriculaTranscricao.cartorio.id,
                            nomeCartorio: vm.matriculaTranscricao.cartorio.nome,
                            numero: vm.matriculaTranscricao.numero,
                            dataMatricula: vm.matriculaTranscricao.dataMatricula,
                            dataRegistro: vm.matriculaTranscricao.dataRegistro,
                            cartorioEstrangeiro: vm.matriculaTranscricao.cartorioEstrangeiro
                        });

                        vm.paginacaoMatriculasTranscricao();
                        vm.matriculaTranscricao = {};
                        vm.isMatricula = false;
                        vm.isMostrarBotaoMatricula = true;
                        vm.editar.index = null;
                        vm.editar.buttonShow = false;
                    }, function () {
                        $mdDialog.cancel();
                    });
                };

                vm.adicionarMatriculaTranscricao = function (formularioMatriculas) {
                    var confirm = $mdDialog.confirm({focusOnOpen: false})
                        .title($filter('translate')('label-titulo-confirmacao'))
                        .textContent($filter('translate')('mensagem-inclusao-registro-aplicada-cadastro-imovel-confirm'))
                        .ok($filter('translate')('btn-sim'))
                        .cancel($filter('translate')('btn-nao'));
                    $mdDialog.show(confirm).then(function () {
                        vm.validarFormulario(formularioMatriculas);

                        if (!formularioMatriculas.$valid) {
                            return;
                        }

                        vm.listaMatriculasTranscricao.push({
                            id: null,
                            uf: vm.matriculaTranscricao.uf,
                            municipio: vm.matriculaTranscricao.municipio.nome,
                            cartorioID: vm.matriculaTranscricao.cartorio.id,
                            nomeCartorio: vm.matriculaTranscricao.cartorio.nome,
                            numero: vm.matriculaTranscricao.numero,
                            dataMatricula: vm.matriculaTranscricao.dataMatricula,
                            dataRegistro: vm.matriculaTranscricao.dataRegistro,
                            cartorioEstrangeiro: vm.matriculaTranscricao.cartorioEstrangeiro
                        });

                        vm.paginacaoMatriculasTranscricao();
                        vm.matriculaTranscricao = {};
                        vm.isMatricula = false;
                        vm.isMostrarBotaoMatricula = true;
                    }, function () {
                        $mdDialog.cancel();
                    });
                };

                vm.excluirMatriculaTranscricao = function ($index) {
                    var confirm = $mdDialog.confirm({focusOnOpen: false})
                        .title($filter('translate')('label-titulo-confirmacao'))
                        .textContent($filter('translate')('mensagem-exclusao-registro-aplicada-cadastro-imovel-confirm'))
                        .ok($filter('translate')('btn-sim'))
                        .cancel($filter('translate')('btn-nao'));
                    $mdDialog.show(confirm).then(function () {
                        vm.listaMatriculasTranscricao.splice($index, 1);
                        vm.paginacaoMatriculasTranscricao();
                    }, function () {
                        $mdDialog.cancel();
                    });
                };

                vm.$watch('listaMatriculasTranscricao', function () {
                    vm.paginacaoMatriculasTranscricao();
                });

                vm.paginacaoMatriculasTranscricao = function () {
                    vm.paginationMatriculaTranscricao = [];
                    vm.totalItems = vm.listaMatriculasTranscricao.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationMatriculaTranscricao = vm.listaMatriculasTranscricao;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationMatriculaTranscricao.push(vm.listaMatriculasTranscricao[i]);
                            if (vm.paginationMatriculaTranscricao.length == vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                init();
            }
        };
    }

})();

(function () {

    directive.$inject = ["imagemAvaliacaoService", "ComumFactory", "$filter"];
    angular.module('su-avaliacao')
        .directive('inserirImagemUnidadeAutonoma', directive);

    function directive (imagemAvaliacaoService, ComumFactory, $filter) {
        return {
            restrict: 'EA',
            scope: {
                isVistoria: '=',
                listaFotosUnidadeAutonoma: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '=',
                nomeArquivo: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/inserirImagemUnidadeAutonoma.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.IMAGEM_UNIDADE_AUTONOMA = 'IMAGEM_UNIDADE_AUTONOMA';

                vm.foto = {};
                vm.descricaoFoto = null;

                vm.paginacaoFotos = [];
                var MEDIA_TYPE_JPEG = 'image/pjpeg';
                var MEDIA_TYPE_JPG = 'image/jpeg';
                var MEDIA_TYPE_PNG = 'image/png';

                var init = function() {

                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;

                    vm.totalItems = 0;

                    vm.paginarListagem();
                };

                vm.adicionarFoto = function(file) {
                    if(file[0].type == MEDIA_TYPE_JPEG || file[0].type == MEDIA_TYPE_JPG
                        || file[0].type == MEDIA_TYPE_PNG) {
                        imagemAvaliacaoService.uploadImagem(file[0], vm.IMAGEM_UNIDADE_AUTONOMA).then(function(resp) {
                            vm.foto = resp.data;
                        });
                    }else{
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-invalido'));
                    }
                };

                vm.adicionarListaFotos = function() {
                    if (!vm.foto || !vm.descricaoFoto || !vm.foto.nomeGerado) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }
                    if (!vm.listaFotosUnidadeAutonoma) {
                        vm.listaFotosUnidadeAutonoma = [];
                    }
                    vm.foto.isAlterado = true;
                    vm.foto.descricao = vm.descricaoFoto;
                    vm.listaFotosUnidadeAutonoma.push(vm.foto);

                    if(vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos){
                        for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                            if (vm.anexoLaudoRelatorio.anexos[i].nome === 'Fotos da Vistoria') {
                                if (!vm.anexoLaudoRelatorio.anexos[i].filhos || !vm.anexoLaudoRelatorio.anexos[i].filhos.length) {
                                    vm.anexoLaudoRelatorio.anexos[i].filhos = [];
                                }

                                vm.anexoLaudoRelatorio.anexos[i].filhos.push({
                                    nome: vm.nomeArquivo,
                                    selecionado: false,
                                    ordem: vm.anexoLaudoRelatorio.anexos[i].filhos.length,
                                    imagemUnidadeAutonoma: vm.foto
                                });
                            }
                        }
                    }

                    vm.paginarListagem();
                    vm.foto = {};
                    vm.descricaoFoto = null;
                };

                vm.removerFotoImovel = function($index, idArquivo) {
                    if (confirm('Deseja remover este arquivo?')) {
                        imagemAvaliacaoService.deletarImagem(idArquivo).then(function() {
                            vm.listaFotosUnidadeAutonoma.splice($index, 1);
                            vm.paginarListagem();
                        }, function () {
                            vm.listaFotosUnidadeAutonoma.splice($index, 1);
                            vm.paginarListagem();
                        });
                        if(vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos){
                            for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                                if (vm.anexoLaudoRelatorio.anexos[i].nome === 'Fotos da Vistoria') {
                                    if (vm.anexoLaudoRelatorio.anexos[i].filhos) {
                                        for (var j = 0; j < vm.anexoLaudoRelatorio.anexos[i].filhos.length; j++) {
                                            if (vm.anexoLaudoRelatorio.anexos[i].filhos[j].imagemUnidadeAutonoma.id === idArquivo) {
                                                vm.anexoLaudoRelatorio.anexos[i].filhos.splice(j, 1);
                                                break;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                };

                vm.$watch('listaFotosUnidadeAutonoma', function () {
                    vm.paginarListagem();
                });

                vm.baixarImagem = function(idImagem) {
                    return imagemAvaliacaoService.downloadImagem(idImagem, vm.IMAGEM_UNIDADE_AUTONOMA);
                };

                vm.paginarListagem = function() {
                    if (vm.listaFotosUnidadeAutonoma) {
                        vm.totalItems = vm.listaFotosUnidadeAutonoma.length;

                        vm.paginacaoFotos = [];
                        vm.totalItems = vm.listaFotosUnidadeAutonoma.length;
                        if (vm.totalItems < vm.tamanhoLimite) {
                            vm.paginacaoFotos = vm.listaFotosUnidadeAutonoma;
                        } else {
                            for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                                vm.paginacaoFotos.push(vm.listaFotosUnidadeAutonoma[i]);
                                if (vm.paginacaoFotos.length == vm.tamanhoLimite) {
                                    break;
                                }
                            }
                        }
                    }
                };

                init();
            }
        };
    };

})();

(function () {

    directive.$inject = ["imagemAvaliacaoService", "ComumFactory", "$filter"];
    angular.module('su-avaliacao')
        .directive('inserirImagemImovel', directive);

    function directive (imagemAvaliacaoService, ComumFactory, $filter) {
        return {
            restrict: 'EA',
            scope: {
                isVistoria: '=',
                listaFotosImovel: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '=',
                nomeArquivo: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/inserirImagemImovel.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.IMAGEM_IMOVEL = 'IMAGEM_IMOVEL';
                vm.IMAGEM_BENFEITORIA = 'IMAGEM_BENFEITORIA';
                vm.IMAGEM_UNIDADE_AUTONOMA = 'IMAGEM_UNIDADE_AUTONOMA';

                vm.foto = {};
                vm.paginacaoFotosImovel = [];

                vm.descricaoFoto = null;

                var MEDIA_TYPE_JPEG = 'image/pjpeg';
                var MEDIA_TYPE_JPG = 'image/jpeg';
                var MEDIA_TYPE_PNG = 'image/png';

                var init = function() {

                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;

                    vm.paginarListagemImovel();
                };

                vm.$watch('listaFotosImovel', function () {
                    vm.paginarListagemImovel();
                });

                vm.adicionarFoto = function(file) {
                    if(file && file[0].type == MEDIA_TYPE_JPEG || file[0].type == MEDIA_TYPE_JPG
                        || file[0].type == MEDIA_TYPE_PNG) {
                        imagemAvaliacaoService.uploadImagem(file[0], vm.IMAGEM_IMOVEL).then(function(resp) {
                            vm.foto = resp.data;
                        });
                    }else{
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-invalido'));
                    }
                };

                vm.adicionarListaFotos = function() {

                    if (!vm.foto || !vm.descricaoFoto || !vm.foto.nomeImagem) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }
                    if (!vm.listaFotosImovel) {
                        vm.listaFotosImovel = [];
                    }
                    vm.foto.isAlterado = true;
                    vm.foto.descricao = vm.descricaoFoto;
                    vm.listaFotosImovel.push(vm.foto);

                    if(vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos){
                        for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                            if (vm.anexoLaudoRelatorio.anexos[i].nome === 'Fotos da Vistoria') {
                                if (!vm.anexoLaudoRelatorio.anexos[i].filhos || !vm.anexoLaudoRelatorio.anexos[i].filhos.length) {
                                    vm.anexoLaudoRelatorio.anexos[i].filhos = [];
                                }

                                vm.anexoLaudoRelatorio.anexos[i].filhos.push({
                                    nome: vm.nomeArquivo,
                                    selecionado: false,
                                    ordem: vm.anexoLaudoRelatorio.anexos[i].filhos.length,
                                    imagemImovel: vm.foto
                                });
                            }
                        }
                    }

                    vm.paginarListagemImovel();
                    vm.foto = {};
                    vm.descricaoFoto = null;
                };

                vm.removerFotoImovel = function($index, idArquivo) {
                    if (confirm('Deseja remover este arquivo?')) {
                        imagemAvaliacaoService.deletarImagem(idArquivo).then(function() {
                            vm.listaFotosImovel.splice($index, 1);
                            vm.paginarListagemImovel();
                        },function () {
                            vm.listaFotosImovel.splice($index, 1);
                            vm.paginarListagemImovel();
                        });
                        if(vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos){
                            for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                                if (vm.anexoLaudoRelatorio.anexos[i].nome === 'Fotos da Vistoria') {
                                    if (vm.anexoLaudoRelatorio.anexos[i].filhos) {
                                        for (var j = 0; j < vm.anexoLaudoRelatorio.anexos[i].filhos.length; j++) {
                                            if (vm.anexoLaudoRelatorio.anexos[i].filhos[j].imagemImovel.id === idArquivo) {
                                                vm.anexoLaudoRelatorio.anexos[i].filhos.splice(j, 1);
                                                break;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                };

                vm.baixarImagem = function(idImagem) {
                    return imagemAvaliacaoService.downloadImagem(idImagem, vm.IMAGEM_IMOVEL);
                };

                vm.paginarListagemImovel = function() {
                    if (vm.listaFotosImovel) {
                        vm.totalItems = vm.listaFotosImovel.length;

                        vm.paginacaoFotosImovel = [];
                        vm.totalItems = vm.listaFotosImovel.length;
                        if (vm.totalItems < vm.tamanhoLimite) {
                            vm.paginacaoFotosImovel = vm.listaFotosImovel;
                        } else {
                            for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                                vm.paginacaoFotosImovel.push(vm.listaFotosImovel[i]);
                                if (vm.paginacaoFotosImovel.length == vm.tamanhoLimite) {
                                    break;
                                }
                            }
                        }
                    }
                };

                init();
            }
        };
    };

})();

(function () {

    directive.$inject = ["imagemAvaliacaoService", "ComumFactory", "$filter"];
    angular.module('su-avaliacao')
        .directive('inserirImagemBenfeitoria', directive);

    function directive (imagemAvaliacaoService, ComumFactory, $filter) {
        return {
            restrict: 'EA',
            scope: {
                isVistoria: '=',
                listaFotosBenfeitoria: '=',
                idBenfeitoria: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '=',
                nomeArquivo: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/inserirImagemBenfeitoria.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.IMAGEM_BENFEITORIA = 'IMAGEM_BENFEITORIA';

                vm.foto = {};
                vm.paginacaoFotos = [];

                vm.descricaoFoto = null;

                var MEDIA_TYPE_JPEG = 'image/pjpeg';
                var MEDIA_TYPE_JPG = 'image/jpeg';
                var MEDIA_TYPE_PNG = 'image/png';

                var init = function() {

                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;

                    vm.paginarListagem();
                };

                vm.adicionarFoto = function(file) {
                    if(file[0].type == MEDIA_TYPE_JPEG || file[0].type == MEDIA_TYPE_JPG
                        || file[0].type == MEDIA_TYPE_PNG) {
                        imagemAvaliacaoService.uploadImagem(file[0], vm.IMAGEM_BENFEITORIA).then(function(resp) {
                            vm.foto = resp.data;
                        });
                    }else{
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-invalido'));
                    }
                };

                vm.adicionarListaFotos = function() {

                    if (!vm.foto || !vm.descricaoFoto || !vm.foto.nomeArquivo) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }
                    if (!vm.listaFotosBenfeitoria) {
                        vm.listaFotosBenfeitoria = [];
                    }
                    vm.foto.isAlterado = true;
                    vm.foto.descricao = vm.descricaoFoto;
                    if (!vm.listaFotosBenfeitoria[vm.idBenfeitoria]) {
                        vm.listaFotosBenfeitoria[vm.idBenfeitoria] = [];
                    }
                    vm.listaFotosBenfeitoria[vm.idBenfeitoria].push(vm.foto);

                    if(vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos){
                        for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                            if (vm.anexoLaudoRelatorio.anexos[i].nome === 'Fotos da Vistoria') {
                                if (!vm.anexoLaudoRelatorio.anexos[i].filhos || !vm.anexoLaudoRelatorio.anexos[i].filhos.length) {
                                    vm.anexoLaudoRelatorio.anexos[i].filhos = [];
                                }

                                vm.anexoLaudoRelatorio.anexos[i].filhos.push({
                                    nome: vm.nomeArquivo,
                                    selecionado: false,
                                    ordem: vm.anexoLaudoRelatorio.anexos[i].filhos.length,
                                    imagemBenfeitoria: vm.foto
                                });
                            }
                        }
                    }

                    vm.paginarListagem();
                    vm.foto = {};
                    vm.descricaoFoto = null;
                };

                vm.removerFotoImovel = function($index, idArquivo) {
                    if (confirm('Deseja remover este arquivo?')) {
                        imagemAvaliacaoService.deletarImagem(idArquivo).then(function() {
                            vm.listaFotosBenfeitoria[vm.idBenfeitoria].splice($index, 1);
                            vm.paginarListagem();
                        }, function () {
                            vm.listaFotosBenfeitoria[vm.idBenfeitoria].splice($index, 1);
                            vm.paginarListagem();
                        });
                        if(vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos){
                            for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                                if (vm.anexoLaudoRelatorio.anexos[i].nome === 'Fotos da Vistoria') {
                                    if (vm.anexoLaudoRelatorio.anexos[i].filhos) {
                                        for (var j = 0; j < vm.anexoLaudoRelatorio.anexos[i].filhos.length; j++) {
                                            if (vm.anexoLaudoRelatorio.anexos[i].filhos[j].imagemBenfeitoria.id === idArquivo) {
                                                vm.anexoLaudoRelatorio.anexos[i].filhos.splice(j, 1);
                                                break;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }


                };

                vm.$watch('listaFotosBenfeitoria', function () {
                    vm.paginarListagem();
                });

                vm.baixarImagem = function(idImagem) {
                    return imagemAvaliacaoService.downloadImagem(idImagem, vm.IMAGEM_BENFEITORIA);
                };

                vm.paginarListagem = function() {
                    if (vm.listaFotosBenfeitoria && vm.listaFotosBenfeitoria[vm.idBenfeitoria]) {
                        vm.totalItems = vm.listaFotosBenfeitoria[vm.idBenfeitoria].length;

                        vm.paginacaoFotos = [];
                        vm.totalItems = vm.listaFotosBenfeitoria[vm.idBenfeitoria].length;
                        if (vm.totalItems < vm.tamanhoLimite) {
                            vm.paginacaoFotos = vm.listaFotosBenfeitoria[vm.idBenfeitoria];
                        } else {
                            for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                                vm.paginacaoFotos.push(vm.listaFotosBenfeitoria[vm.idBenfeitoria][i]);
                                if (vm.paginacaoFotos.length == vm.tamanhoLimite) {
                                    break;
                                }
                            }
                        }
                    }
                };

                init();
            }
        };
    };

})();
(function () {

    angular.module('su-avaliacao')
        .directive('enquadramentoLaudoGrauPrecisao', directive);

    function directive () {
        return {
            restrict: 'EA',
            scope: {
                avaliacao: '=',
                detalharLaudoRelatorio: '=',
                isAssinar: '=',
                labelGrau: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/enquadramentoLaudoSegundoGrauPrecisao.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.colorTableColumnAvaliacao = {
                    'background-color': 'lightgreen'
                };

                vm.$watch('avaliacao.enquadramento.amplitudeIntervalo', function() {
                    if(vm.avaliacao) {
                        if (vm.avaliacao.enquadramento.amplitudeIntervalo <= 30) {
                            vm.labelGrau = 'Grau III';
                        } else if (vm.avaliacao.enquadramento.amplitudeIntervalo > 30 && vm.avaliacao.enquadramento.amplitudeIntervalo <= 40) {
                            vm.labelGrau = 'Grau II';
                        } else if (vm.avaliacao.enquadramento.amplitudeIntervalo > 40 && vm.avaliacao.enquadramento.amplitudeIntervalo <= 50) {
                            vm.labelGrau = 'Grau I';
                        } else {
                            vm.labelGrau = 'O laudo de avaliação não possui classificação do resultado quanto à precisão';
                        }
                    }
                });

            }
        };
    };

})();
(function () {

    directive.$inject = ["avaliacaoLaudoService"];
    angular.module('su-avaliacao')
        .directive('enquadramentoLaudoGrauFundamentacao', directive);

    function directive(avaliacaoLaudoService) {
        return {
            restrict: 'EA',
            scope: {
                avaliacao: '=',
                detalharLaudoRelatorio: '=',
                isAssinar: '=',
                enquadramentoGrau3: '=',
                enquadramentoGrau2: '=',
                enquadramentoGrau1: '=',
                enquadramentoGrauNaoAtingido: '=',
                medirGrauEnquadramentoFundamentacao: '=',
                labelGrau: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/enquadramentoLaudoSegundoGrauFundamentacao.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaNivelFundamentacao = [];
                vm.labelPontosObtidos = null;
                vm.somatorioPontosObtidos = 0;
                vm.NAO_ATINGIDO = 'NAO_ATINGIDO';

                vm.labelGrau = '';

                vm.$watchGroup(['enquadramentoGrau3', 'enquadramentoGrau2', 'enquadramentoGrau1'], function () {
                    if (vm.enquadramentoGrau3) {
                        vm.labelGrau = 'Grau III';
                    } else if (vm.enquadramentoGrau2) {
                        vm.labelGrau = 'Grau II';
                    } else if (vm.enquadramentoGrau1) {
                        vm.labelGrau = 'Grau I';
                    } else {
                        vm.labelGrau = 'O laudo de avaliação não possui enquadramento quanto ao Grau fundamentação';
                    }
                });

                var valoresNivelFundamentacao = {
                    UM: 1,
                    DOIS: 2,
                    TRES: 3,
                    NAO_ATINGIDO: 0
                };

                var TRES = 'TRES';
                var DOIS = 'DOIS';
                var UM = 'UM';

                vm.colorTableColumnAvaliacao = {
                    'background-color': 'lightgreen'
                };

                var init = function () {
                    buscarNiveisFundamentacao();
                };

                vm.$watch('avaliacao.enquadramento', function () {
                    if (vm.avaliacao && vm.avaliacao.enquadramento) {
                        vm.contarPontosObtidosFundamentacao();
                        if (vm.medirGrauEnquadramentoFundamentacao) {
                            vm.medirGrauEnquadramentoFundamentacao();
                        }

                        avaliacaoLaudoService.buscarNivelFundamentacao().then(function (resp) {
                            vm.listaNivelFundamentacao = resp.data;

                            vm.labelcaracterizacaoImovelAvaliado = validarNivelFundamentacao(vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado);
                            vm.labelquantidadeMinimaDados = validarNivelFundamentacao(vm.avaliacao.enquadramento.quantidadeMinimaDados);
                            vm.labelidentificacaoDadosMercado = validarNivelFundamentacao(vm.avaliacao.enquadramento.identificacaoDadosMercado);
                            vm.labelextrapolacao = validarNivelFundamentacao(vm.avaliacao.enquadramento.extrapolacao);
                            vm.labelnivelSignificanciaMaximoRejeicao = validarNivelFundamentacao(vm.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao);
                            vm.labelnivelSignificanciaMaximoAdmitido = validarNivelFundamentacao(vm.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido);
                        });
                    }
                });

                var buscarNiveisFundamentacao = function () {
                    avaliacaoLaudoService.buscarNivelFundamentacao().then(function (resp) {
                        vm.listaNivelFundamentacao = resp.data;
                    });
                };

                vm.contarPontosObtidosFundamentacao = function () {
                    var anterior = false;
                    vm.labelPontosObtidos = '';
                    vm.somatorioPontosObtidos = 0;
                    if (vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado) {
                        vm.somatorioPontosObtidos += valoresNivelFundamentacao[vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado];
                        if (vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado == vm.NAO_ATINGIDO) {
                            vm.labelPontosObtidos += '1';
                            anterior = true;
                        }
                    }
                    if (vm.avaliacao.enquadramento.quantidadeMinimaDados) {
                        vm.somatorioPontosObtidos += valoresNivelFundamentacao[vm.avaliacao.enquadramento.quantidadeMinimaDados];
                        if (vm.avaliacao.enquadramento.quantidadeMinimaDados == vm.NAO_ATINGIDO) {
                            vm.labelPontosObtidos += (anterior ? ',' : '');
                            vm.labelPontosObtidos += '2';
                            anterior = true;
                        }
                    }
                    if (vm.avaliacao.enquadramento.identificacaoDadosMercado) {
                        vm.somatorioPontosObtidos += valoresNivelFundamentacao[vm.avaliacao.enquadramento.identificacaoDadosMercado];
                        if (vm.avaliacao.enquadramento.identificacaoDadosMercado == vm.NAO_ATINGIDO) {
                            vm.labelPontosObtidos += (anterior ? ',' : '');
                            vm.labelPontosObtidos += '3';
                            anterior = true;
                        }
                    }
                    if (vm.avaliacao.enquadramento.extrapolacao) {
                        vm.somatorioPontosObtidos += valoresNivelFundamentacao[vm.avaliacao.enquadramento.extrapolacao];
                        if (vm.avaliacao.enquadramento.extrapolacao == vm.NAO_ATINGIDO) {
                            vm.labelPontosObtidos += (anterior ? ',' : '');
                            vm.labelPontosObtidos += '4';
                            anterior = true;
                        }
                    }
                    if (vm.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao) {
                        vm.somatorioPontosObtidos += valoresNivelFundamentacao[vm.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao];
                        if (vm.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao == vm.NAO_ATINGIDO) {
                            vm.labelPontosObtidos += (anterior ? ',' : '');
                            vm.labelPontosObtidos += '5';
                            anterior = true;
                        }
                    }
                    if (vm.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido) {
                        vm.somatorioPontosObtidos += valoresNivelFundamentacao[vm.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido];
                        if (vm.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido == vm.NAO_ATINGIDO) {
                            vm.labelPontosObtidos += (anterior ? ',' : '');
                            vm.labelPontosObtidos += '6';
                        }
                    }
                };


                var validarNivelFundamentacao = function (nivelFundamentacao) {
                    var labelNivel = '';
                    angular.forEach(vm.listaNivelFundamentacao, function (nivelFundamento) {
                        if (nivelFundamentacao == nivelFundamento.name) {
                            labelNivel = nivelFundamento.nivel;
                        }
                    });
                    return labelNivel;
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["dominioAvaliacaoService", "formularioAvaliacaoService", "$filter", "ComumFactory", "arquivoAvaliacaoService", "$mdDialog", "informacaoAnexoService", "documentoCartorialService"];
    angular.module('su-avaliacao')
        .directive('documentosCartoriais', directive);

    function directive(dominioAvaliacaoService, formularioAvaliacaoService, $filter, ComumFactory,
                       arquivoAvaliacaoService, $mdDialog, informacaoAnexoService, documentoCartorialService) {
        return {
            restrict: 'EA',
            scope: {
                uf: '=',
                listaCertidoesCartoriais: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/documentosCartoriais.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                vm.validarFormulario = formularioServiceInstance.validarFormulario;

                vm.documentoCartorial = {};
                vm.listaCartorios = [];
                vm.arquivoTemp = {};

                vm.paginationDocumentosCartoriais = [];

                vm.isDocumento = false;
                vm.mostrarBotaoCartorial = true;

                var MEDIA_TYPE_PDF = 'application/pdf';

                var init = function () {
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;

                    vm.paginacaoDocumentosCartoriais();
                };

                vm.inserirNovoDocumentoCartoial = function () {
                    vm.isDocumento = true;
                    vm.mostrarBotaoCartorial = false;
                };

                vm.$watch('uf', function (uf) {
                    if (uf && uf != '') {
                        dominioAvaliacaoService.pesquisarCartoriosPorUfIntegrador(uf)
                            .then(function (resp) {
                                vm.listaCartorios = resp.data;
                            });
                    }
                });

                vm.adicionarArquivoDocumentoCartorial = function (file) {
                    if (file) {
                        if (file[0].type == MEDIA_TYPE_PDF) {
                            arquivoAvaliacaoService.uploadArquivo(file[0]).then(function (resp) {
                                vm.documentoCartorial.arquivo = resp.data;
                            });
                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-formato-invalido'));
                        }
                    }
                };

                vm.adicionarListaDocumentosCartoriais = function () {
                    if (!vm.documentoCartorial || !vm.documentoCartorial.nome || !vm.documentoCartorial.cartorio || !vm.documentoCartorial.arquivo) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }

                    vm.listaCertidoesCartoriais.push({
                        nome: vm.documentoCartorial.nome,
                        idCartorio: vm.documentoCartorial.cartorio.id,
                        nomeCartorio: vm.documentoCartorial.cartorio.nome,
                        arquivo: vm.documentoCartorial.arquivo
                    });

                    if (vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos) {
                        for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                            if (vm.anexoLaudoRelatorio.anexos[i].nome === 'Documento Cartorial') {
                                if (!vm.anexoLaudoRelatorio.anexos[i].filhos || !vm.anexoLaudoRelatorio.anexos[i].filhos.length) {
                                    vm.anexoLaudoRelatorio.anexos[i].filhos = [];
                                }

                                vm.anexoLaudoRelatorio.anexos[i].filhos.push({
                                    nome: vm.documentoCartorial.nome,
                                    selecionado: false,
                                    ordem: vm.anexoLaudoRelatorio.anexos[i].filhos.length,
                                    arquivo: vm.documentoCartorial.arquivo
                                });
                            }
                        }
                    }

                    vm.paginacaoDocumentosCartoriais();

                    vm.documentoCartorial = {};
                    vm.isDocumento = false;
                    vm.mostrarBotaoCartorial = true;
                };

                vm.removerDocumentoCartorial = function ($index, documentoId, arquivoId) {
                    var confirm = $mdDialog.confirm({focusOnOpen: false})
                        .title($filter('translate')('label-titulo-confirmacao'))
                        .textContent('Deseja remover este arquivo?')
                        .ok($filter('translate')('btn-sim'))
                        .cancel($filter('translate')('btn-nao'));
                    $mdDialog.show(confirm).then(function () {

                        if (vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos) {
                            for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                                if (vm.anexoLaudoRelatorio.anexos[i].nome === 'Documento Cartorial') {
                                    if (vm.anexoLaudoRelatorio.anexos[i].filhos) {
                                        for (var j = 0; j < vm.anexoLaudoRelatorio.anexos[i].filhos.length; j++) {
                                            if (vm.anexoLaudoRelatorio.anexos[i].filhos[j].arquivo.id == arquivoId) {
                                                var informacaoAnexoId = vm.anexoLaudoRelatorio.anexos[i].filhos[j].id;
                                                vm.anexoLaudoRelatorio.anexos[i].filhos.splice(j, 1);

                                                informacaoAnexoService.deletarInformacaoAnexo(informacaoAnexoId);
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        if (documentoId) {
                            documentoCartorialService.deletarDocumentoCartorial(documentoId).then(function () {
                                vm.listaCertidoesCartoriais.splice($index, 1);
                                vm.paginacaoDocumentosCartoriais();
                            });
                        } else {
                            vm.listaCertidoesCartoriais.splice($index, 1);
                            vm.paginacaoDocumentosCartoriais();
                        }

                    }, function () {
                        $mdDialog.cancel();
                    });
                };

                vm.$watch('listaCertidoesCartoriais', function () {
                    vm.paginacaoDocumentosCartoriais();
                });

                vm.paginacaoDocumentosCartoriais = function () {
                    vm.paginationDocumentosCartoriais = [];
                    vm.totalItems = vm.listaCertidoesCartoriais.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationDocumentosCartoriais = vm.listaCertidoesCartoriais;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationDocumentosCartoriais.push(vm.listaCertidoesCartoriais[i]);
                            if (vm.paginationDocumentosCartoriais.length == vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["arquivoAvaliacaoService", "ComumFactory", "$filter", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('documentoLaudoRelatorioPadrao', directive);

    function directive(arquivoAvaliacaoService, ComumFactory, $filter, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                listaDocumentosLaudo: '=',
                detalharLaudoAvaliacao: '=',
                tipoValidacao: '=',
                fileLimit: '=?',
                customNomeLabel: '=?',
                customButtonLabel: '=?',
                campoData: '=?',
                titulo: '=?',
                hint: '=?',
                anexoLaudoRelatorio: '=?',
                tipoAnexo: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/documentosLaudoRelatorioPadrao.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.arquivoTemp = {};

                var content_type1 = ['application/pdf','image/pjpeg','image/jpeg','image/png'];
                var content_type2 = ['application/pdf', 'image/jpeg','image/pjpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.oasis.opendocument.text ', 'application/vnd.oasis.opendocument.spreadsheet'];

                var CONTENT_TYPE_01 = 'CONTENT_TYPE_01';
                var CONTENT_TYPE_02 = 'CONTENT_TYPE_02';
                var CONTENT_TYPE_03 = 'CONTENT_TYPE_03';

                vm.documento = {};
                vm.isDocumento = false;
                vm.dataAtual = new Date();

                vm.paginationDocumentos = [];

                var init = function () {
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;
                };

                vm.adicionarArquivoDocumento = function (file) {

                    console.log('file2');
                    console.log(file);

                    if((content_type1.indexOf(file[0].type) !== -1 && vm.tipoValidacao===CONTENT_TYPE_01)
                        || (content_type2.indexOf(file[0].type) !== -1 && vm.tipoValidacao===CONTENT_TYPE_02)
                        || (vm.tipoValidacao===CONTENT_TYPE_03)) {
                        arquivoAvaliacaoService.uploadArquivo(file[0]).then(function(resp) {
                            vm.documento.arquivo = resp.data;
                        });
                    }else{
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-formato-invalido'));
                    }
                };

                vm.$watch('listaDocumentosLaudo', function () {

                    if (vm.fileLimit && vm.fileLimit == 1) {
                        if (vm.listaDocumentosLaudo) {
                            vm.paginationDocumentos = [vm.listaDocumentosLaudo];
                            vm.totalItems = vm.paginationDocumentos.length;
                        }
                    } else {
                        if (!vm.listaDocumentosLaudo) {
                            vm.listaDocumentosLaudo = [];
                        }
                        vm.paginacaoDocumentos();
                    }
                });

                vm.adicionarListaDocumentos = function () {
                    vm.formEnviado = true;

                    if (!vm.documento || !vm.documento.nome || !vm.documento.data || !vm.documento.arquivo) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }

                    if (!vm.listaDocumentosLaudo) {
                        vm.listaDocumentosLaudo = [];
                    }

                    if (vm.fileLimit && vm.fileLimit == 1) {
                        vm.listaDocumentosLaudo = angular.copy(vm.documento);
                        vm.paginationDocumentos = [vm.listaDocumentosLaudo];
                        vm.totalItems = vm.paginationDocumentos.length;
                        vm.documento = {};
                        return;
                    }

                    if(vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos){
                        for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                            if (vm.anexoLaudoRelatorio.anexos[i].nome === vm.tipoAnexo) {
                                if (!vm.anexoLaudoRelatorio.anexos[i].filhos || !vm.anexoLaudoRelatorio.anexos[i].filhos.length) {
                                    vm.anexoLaudoRelatorio.anexos[i].filhos = [];
                                }

                                vm.anexoLaudoRelatorio.anexos[i].filhos.push({
                                    nome: vm.documento.nome,
                                    selecionado: false,
                                    ordem: vm.anexoLaudoRelatorio.anexos[i].filhos.length,
                                    arquivo: vm.documento.arquivo
                                });
                            }
                        }
                    }

                    vm.listaDocumentosLaudo.push(vm.documento);
                    vm.formEnviado = false;

                    vm.paginacaoDocumentos();
                    vm.documento = {};
                };

                vm.removerDocumento = function ($index, idArquivo) {

                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja remover este arquivo?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {

                        if(vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos){
                            for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                                if (vm.anexoLaudoRelatorio.anexos[i].nome === vm.tipoAnexo) {
                                    if (vm.anexoLaudoRelatorio.anexos[i].filhos) {
                                        for (var j = 0; j < vm.anexoLaudoRelatorio.anexos[i].filhos.length; j++) {
                                            if (vm.anexoLaudoRelatorio.anexos[i].filhos[j].arquivo.id === idArquivo) {
                                                vm.anexoLaudoRelatorio.anexos[i].filhos.splice(j, 1);
                                                break;
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        arquivoAvaliacaoService.deletarArquivo(idArquivo).then(function () {
                            vm.listaDocumentosLaudo.splice($index, 1);
                            vm.paginacaoDocumentos();
                        });
                    });
                };

                vm.paginacaoDocumentos = function () {
                    vm.totalItems = vm.listaDocumentosLaudo.length;

                    vm.paginationDocumentos = [];
                    vm.totalItems = vm.listaDocumentosLaudo.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationDocumentos = vm.listaDocumentosLaudo;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationDocumentos.push(vm.listaDocumentosLaudo[i]);
                            if (vm.paginationDocumentos.length == vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                vm.fileLimitValido = function () {
                    return !vm.fileLimit || (vm.fileLimit > (vm.listaDocumentosLaudo ? vm.listaDocumentosLaudo.length : 0));
                };

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                init();
            }
        };
    }
})();


(function () {

    directive.$inject = ["imagemAvaliacaoService"];
    angular.module('su-avaliacao')
        .directive('accordeonArquivoVistoria', directive);

    function directive (imagemAvaliacaoService) {
        return {
            restrict: 'EA',
            scope: {
                isVistoria: '=',
                listaFotosImovel: '=',
                listaFotosBenfeitoria: '=',
                listaFotosUnidadeAutonoma: '=',
                listaBenfeitoria: '=',
                unidadeAutonoma: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/accordeonArquivoVistoria.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.IMAGEM_IMOVEL = 'IMAGEM_IMOVEL';
                vm.IMAGEM_BENFEITORIA = 'IMAGEM_BENFEITORIA';
                vm.IMAGEM_UNIDADE_AUTONOMA = 'IMAGEM_UNIDADE_AUTONOMA';

                var init = function() {

                };

                vm.gerarImagem = function(base64, contentType) {
                    return imagemAvaliacaoService.gerarPreviewImagem(base64, contentType);
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["dominioPlenoService", "ComumFactory", "$filter", "$state"];
    angular.module('su-avaliacao')
        .directive('inserirValoresTerrenoBenfeitoria', directive);

    function directive(dominioPlenoService, ComumFactory, $filter, $state) {
        return {
            restrict: 'EA',
            scope: {
                ripImovel: '=',
                assinatura: '=?',
                listaBenfeitoriasImovel: '=',
                unidadeAutonomaImovel: '=',
                tipoValorLaudo: '=',
                detalharLaudoRelatorio: '=',
                redirecionarInformarValorCub: '=',
                redirecionarInformarValorEdificacao: '=',
                totalValorTerreno: '=',
                tipoLaudo: '=',
                valorAdotadoSelecionado: '=',
                valorTerreno: '=',
                moeda: '=',
                unidadeMedida: '=',
                listaMoedas: '=?',
                paisImovel: '=',
                validarValorImovel: '=',
                termoResponsabilidade: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/inserirValoresTerrenoBenfeitoria.html',
            link: function ($scope) {

                var vm = $scope;

                vm.INFORMAR_VALORES_LAUDO = 'INFORMAR_VALORES_LAUDO';
                vm.ESTIMAR_VALORES = 'ESTIMAR_VALORES';
                vm.ESTIMAR_VALORES_TERRENO = 'ESTIMAR_VALORES_TERRENO';

                vm.LAUDO_RELATORIO = 'LAUDO_RELATORIO';
                vm.LAUDO_AVALIACAO = 'LAUDO_AVALIACAO';

                vm.BRASIL = 'Brasil';
                vm.mostrarEstimarValorTerreno = false;
                vm.tipoValorLaudo = vm.INFORMAR_VALORES_LAUDO;
                vm.habilitaTipoValorLaudo = true;

                var init = function () {
                    //informa ao componente pai o valor
                    vm.tipoValorLaudo = vm.INFORMAR_VALORES_LAUDO;
                    /* if (vm.paisImovel !== undefined && vm.paisImovel !== vm.BRASIL) {
                        vm.tipoValorLaudo = vm.INFORMAR_VALORES_LAUDO;
                    } */
                    if ($state.current.name.indexOf("EDITAR_RELATORIO_VALOR_REFERENCIA") !== -1) {
                        vm.habilitaTipoValorLaudo = false;
                    }

                };

                vm.$watch('assinatura', function () {
                    var qtdBenfeitoriaEdificacao = 0;

                    if(!vm.assinatura){
                        vm.assinatura = new Date();
                    }

                    if (vm.listaBenfeitoriasImovel && vm.listaBenfeitoriasImovel.length > 1) {

                        angular.forEach(vm.listaBenfeitoriasImovel, function (benfeitoria) {
                            if(benfeitoria.tipoBenfeitoria && benfeitoria.tipoBenfeitoria.descricao === 'Edificação'){
                                qtdBenfeitoriaEdificacao += 1;
                            }
                            if(benfeitoria.benfeitoriaImovel && benfeitoria.benfeitoriaImovel.tipoBenfeitoria && benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao === 'Edificação'){
                                qtdBenfeitoriaEdificacao += 1;
                            }
                        });
                    }

                    if(qtdBenfeitoriaEdificacao > 1){
                        vm.mostrarEstimarValorTerreno = false;
                    }else {
                        if (vm.assinatura && vm.ripImovel) {
                            dominioPlenoService.buscarValorDominioPlenoPorData(vm.ripImovel, vm.assinatura).then(function (resp) {
                                if (resp.data !== "") {
                                    vm.dominioPleno = resp.data;
                                    vm.mostrarEstimarValorTerreno = true;
                                } else {
                                    vm.mostrarEstimarValorTerreno = false;
                                }
                            });
                        }
                    }
                });

                vm.limparDadosUnidadeAutonomaEBenfeitorias = function () {
                    vm.valorTerreno = null;
                    if (vm.listaBenfeitoriasImovel && vm.listaBenfeitoriasImovel.length > 0) {
                        angular.forEach(vm.listaBenfeitoriasImovel, function (benfeitoria) {
                            benfeitoria.valor = null;
                            benfeitoria.padraoAcabamento = null;
                            benfeitoria.tipoProjetoCub = null;
                            benfeitoria.idadeAparente = null;
                            benfeitoria.vidaUtil = null;
                            benfeitoria.estadoConservacao = null;
                            benfeitoria.valorCub = null;
                            benfeitoria.valorCubDepreciado = null;
                            benfeitoria.estimativas = null;
                            benfeitoria.isFatoresAcrescimo = null;
                            if(benfeitoria.benfeitoriaImovel){
                                benfeitoria.benfeitoriaImovel.valor = null;
                                benfeitoria.benfeitoriaImovel.padraoAcabamento = null;
                                benfeitoria.benfeitoriaImovel.tipoProjetoCub = null;
                                benfeitoria.benfeitoriaImovel.idadeAparente = null;
                                benfeitoria.benfeitoriaImovel.vidaUtil = null;
                                benfeitoria.benfeitoriaImovel.estadoConservacao = null;
                                benfeitoria.benfeitoriaImovel.valorCub = null;
                                benfeitoria.benfeitoriaImovel.valorCubDepreciado = null;
                                benfeitoria.benfeitoriaImovel.estimativas = null;
                                benfeitoria.benfeitoriaImovel.isFatoresAcrescimo = null;
                            }
                        });
                    } else if (vm.unidadeAutonomaImovel && vm.unidadeAutonomaImovel.length > 0) {
                        vm.unidadeAutonomaImovel[0].valor = null;
                        vm.unidadeAutonomaImovel[0].padraoAcabamento = null;
                        vm.unidadeAutonomaImovel[0].tipoProjetoCub = null;
                        vm.unidadeAutonomaImovel[0].estimativas = null;
                        vm.unidadeAutonomaImovel[0].isFatoresAcrescimo = null;
                    }
                };

                vm.buscarValorDominioPleno = function () {
                    if (vm.tipoValorLaudo === vm.ESTIMAR_VALORES_TERRENO) {
                        if (vm.dominioPleno.valorTerreno > vm.valorAdotadoSelecionado) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-dominio-pleno-superior-valor-imovel'));
                            vm.valorTerreno = null;
                            vm.tipoValorLaudo = null;
                        } else {
                            vm.valorTerreno = vm.dominioPleno.valorTerreno;
                            if (vm.listaBenfeitoriasImovel && vm.listaBenfeitoriasImovel.length > 0) {
                                angular.forEach(vm.listaBenfeitoriasImovel, function (benfeitoria) {
                                    benfeitoria.valor = vm.valorAdotadoSelecionado - vm.valorTerreno;
                                    if(benfeitoria.benfeitoriaImovel){
                                        benfeitoria.benfeitoriaImovel.valor = vm.valorAdotadoSelecionado - vm.valorTerreno;
                                    }
                                });
                            } else if (vm.unidadeAutonomaImovel && vm.unidadeAutonomaImovel.length > 0) {
                                vm.unidadeAutonomaImovel[0].valor = vm.valorAdotadoSelecionado - vm.valorTerreno;
                            }
                        }
                    }
                };

                vm.$watch('unidadeAutonomaImovel', function () {
                    calcularValorTerreno();
                }, true);

                vm.$watch('listaBenfeitoriasImovel', function () {
                    calcularValorTerreno();
                }, true);

                vm.$watch('valorAdotadoSelecionado', function () {
                    calcularValorTerreno();
                }, true);

                var calcularValorTerreno = function () {
                  if (vm.tipoValorLaudo === vm.ESTIMAR_VALORES) {
                      if (vm.tipoLaudo === vm.LAUDO_AVALIACAO) {
                          if (vm.listaBenfeitoriasImovel && vm.listaBenfeitoriasImovel.length > 0) {
                              var totalBenfeitorias = 0;
                              angular.forEach(vm.listaBenfeitoriasImovel, function (benfeitoria) {
                                  totalBenfeitorias += angular.isDefined(benfeitoria.valor) ? benfeitoria.valor : 0;
                              });
                              vm.totalValorTerreno = vm.valorAdotadoSelecionado - totalBenfeitorias;
                          } else if (vm.unidadeAutonomaImovel && vm.unidadeAutonomaImovel.length === 1) {
                              vm.totalValorTerreno = vm.valorAdotadoSelecionado - vm.unidadeAutonomaImovel[0].valor;
                          }
                      } else {
                          if (vm.listaBenfeitoriasImovel && vm.listaBenfeitoriasImovel.length > 0) {
                              var totalBenfeitorias = 0;
                              angular.forEach(vm.listaBenfeitoriasImovel, function (benfeitoria) {
                                  totalBenfeitorias += angular.isDefined(benfeitoria.benfeitoriaImovel.valor) ? benfeitoria.benfeitoriaImovel.valor : 0;
                              });
                              vm.totalValorTerreno = vm.valorAdotadoSelecionado - totalBenfeitorias;
                          } else if (vm.unidadeAutonomaImovel && vm.unidadeAutonomaImovel.length === 1) {
                              vm.totalValorTerreno = vm.valorAdotadoSelecionado - vm.unidadeAutonomaImovel[0].valor;
                          }
                      }
                  }
                };

                init();

            }
        };
    }

})();
(function () {

    angular.module('su-avaliacao')
        .directive('fotosAvaliacaoImovel', directive);

    function directive () {
        return {
            restrict: 'EA',
            scope: {
                listaImagensImovel: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/fotosAvaliacaoImovel.html',
            link: function ($scope) {

                $scope.selectedIndex = 0;

                $scope.imagemAnterior=function(){
                    if($scope.selectedIndex > 0){
                        $scope.selectedIndex--;
                    }
                };

                $scope.proximaImagem=function(){
                    if($scope.selectedIndex <= $scope.listaImagensImovel.length){
                        $scope.selectedIndex++;
                    }
                };

                $scope.gerarImagem = function(base64) {
                    return 'data:image/png;base64,' + base64;
                };

            }
        };
    }
})();
(function () {

    directive.$inject = ["avaliacaoEscopoCompartilhadoService", "arquivoAvaliacaoService", "$state", "$stateParams", "$mdDialog"];
    angular.module('su-avaliacao').directive('documentoEdificacaoUnidadeAutonoma', directive);

    function directive(avaliacaoEscopoCompartilhadoService, arquivoAvaliacaoService, $state, $stateParams, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                unidadeAutonoma: '=',
                unidadeAutonomaIncorporacao: '=',
                listaDocumentosEdificacaoUnidadeAutonoma: '=',
                redirecionarCadastrarDocumentoEdificacaoUnidadeAutonoma: '=',
                detalharLaudoRelatorio: '=',
                customNomeLabel: '=?',
                customButtonLabel: '=?',
                campoData: '=?',
                titulo: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/documentoEdificacaoUnidadeAutonoma.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.arquivoTemp = {};

                vm.documento = {};
                vm.isDocumento = false;

                vm.isBotaoIncluirNovoDocumento = true;
                vm.editar = {};
                vm.editar.buttonShow = false;
                vm.idLaudo = $stateParams.idLaudo;
                vm.paginationDocumentos = [];

                var init = function () {
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;
                };

                vm.inserirNovoDocumento = function () {
                    vm.editar.index = null;
                    vm.editar.buttonShow = false;
                    vm.editar.tipo = 'UNIDADE_AUTONOMA';

                    vm.redirecionarCadastrarDocumentoEdificacaoUnidadeAutonoma(vm.listaDocumentosEdificacaoUnidadeAutonoma, vm.editar);
                };

                vm.$watch('listaDocumentosEdificacaoUnidadeAutonoma', function () {
                    if (!vm.listaDocumentosEdificacaoUnidadeAutonoma) {
                        vm.listaDocumentosEdificacaoUnidadeAutonoma = [];
                    }
                    vm.paginacaoDocumentos();

                });

                vm.alterarDocumentoEdificacao = function ($index) {
                    vm.editar.index = $index;
                    vm.editar.buttonShow = true;
                    vm.editar.tipo = 'UNIDADE_AUTONOMA';

                    vm.redirecionarCadastrarDocumentoEdificacaoUnidadeAutonoma(vm.listaDocumentosEdificacaoUnidadeAutonoma, vm.editar);
                };


                vm.removerDocumento = function ($index, idArquivo) {

                    var confirm = $mdDialog.confirm()
                        .title('Atenção!')
                        .textContent('Deseja remover este arquivo?')
                        .ok('Confirmar')
                        .cancel('Cancelar');

                    $mdDialog.show(confirm).then(function () {
                        arquivoAvaliacaoService.deletarArquivo(idArquivo).then(function () {
                            vm.listaDocumentosEdificacaoUnidadeAutonoma.splice($index, 1);
                            vm.paginacaoDocumentos();
                        });
                    });
                };

                vm.paginacaoDocumentos = function () {
                    vm.totalItems = vm.listaDocumentosEdificacaoUnidadeAutonoma.length;

                    vm.paginationDocumentos = [];
                    vm.totalItems = vm.listaDocumentosEdificacaoUnidadeAutonoma.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationDocumentos = vm.listaDocumentosEdificacaoUnidadeAutonoma;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationDocumentos.push(vm.listaDocumentosEdificacaoUnidadeAutonoma[i]);
                            if (vm.paginationDocumentos.length == vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                init();
            }
        };
    }
})();
(function () {

    directive.$inject = ["avaliacaoEscopoCompartilhadoService", "arquivoAvaliacaoService", "$state", "$stateParams", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('documentoEdificacaoBenfeitoria', directive);

    function directive(avaliacaoEscopoCompartilhadoService, arquivoAvaliacaoService, $state, $stateParams, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                benfeitorias: '=',
                listaBenfeitoriaAvaliacao: '=',
                listaDocumentosEdificacaoBenfeitoria: '=',
                redirecionarCadastrarDocumentoEdificacaoBenfeitoria: '=',
                detalharLaudoRelatorio: '=',
                customNomeLabel: '=?',
                customButtonLabel: '=?',
                campoData: '=?',
                titulo: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/documentoEdificacaoBenfeitoria.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.arquivoTemp = {};

                vm.documento = {};
                vm.isDocumento = false;

                vm.isBotaoIncluirNovoDocumento = true;
                vm.editar = {};
                vm.editar.buttonShow = false;

                vm.idLaudo = $stateParams.idLaudo;
                vm.paginationDocumentos = [];

                var init = function () {
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;

                };


                vm.inserirNovoDocumento = function () {
                    vm.editar.index = null;
                    vm.editar.buttonShow = false;
                    vm.editar.tipo = 'BENFEITORIA';
                    vm.redirecionarCadastrarDocumentoEdificacaoBenfeitoria(vm.listaDocumentosEdificacaoBenfeitoria, vm.editar);
                };

                vm.$watch('listaDocumentosEdificacaoBenfeitoria', function () {
                    if (!vm.listaDocumentosEdificacaoBenfeitoria) {
                        vm.listaDocumentosEdificacaoBenfeitoria = [];
                    }
                    vm.paginacaoDocumentos();

                });

                vm.alterarDocumentoEdificacao = function ($index) {
                    vm.editar.index = $index;
                    vm.editar.buttonShow = true;
                    vm.editar.tipo = 'BENFEITORIA';
                    vm.redirecionarCadastrarDocumentoEdificacaoBenfeitoria(vm.listaDocumentosEdificacaoBenfeitoria, vm.editar);
                };


                vm.removerDocumento = function ($index, idArquivo) {

                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja remover este arquivo?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        arquivoAvaliacaoService.deletarArquivo(idArquivo).then(function () {
                            vm.listaDocumentosEdificacaoBenfeitoria.splice($index, 1);
                            vm.paginacaoDocumentos();
                        });
                    });
                };

                vm.paginacaoDocumentos = function () {
                    vm.totalItems = vm.listaDocumentosEdificacaoBenfeitoria.length;

                    vm.paginationDocumentos = [];
                    vm.totalItems = vm.listaDocumentosEdificacaoBenfeitoria.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationDocumentos = vm.listaDocumentosEdificacaoBenfeitoria;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationDocumentos.push(vm.listaDocumentosEdificacaoBenfeitoria[i]);
                            if (vm.paginationDocumentos.length == vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                init();
            }
        };
    }
})();
(function () {

    directive.$inject = ["arquivoAvaliacaoService", "ComumFactory", "$filter", "$mdDialog", "documentoSeiService"];
    angular.module('su-avaliacao')
        .directive('documentoSei', directive);

    function directive(arquivoAvaliacaoService, ComumFactory, $filter, $mdDialog, documentoSeiService) {
        return {
            restrict: 'EA',
            scope: {
                listaDocumentosLaudo: '=',
                detalharLaudoAvaliacao: '=',
                uf: '=',
                fileLimit: '=?',
                customNomeLabel: '=?',
                customButtonLabel: '=?',
                titulo: '=?',
                tipoFoto: '=?',
                tipoAnexo: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/documentoSei.tmpl.html',
            link: function ($scope) {


                var vm = $scope;

                vm.arquivoTemp = {};

                var content_type = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel',
                    'application/excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.oasis.opendocument.text', 'application/vnd.oasis.opendocument.spreadsheet', 'image/png', 'image/jpeg'];


                vm.documento = {};
                vm.isDocumento = false;
                vm.dataAtual = new Date();

                vm.paginationDocumentos = [];

                var init = function () {
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;
                };

                vm.$watch('listaDocumentosLaudo', function () {

                    if (vm.fileLimit && vm.fileLimit === 1) {
                        if (vm.listaDocumentosLaudo) {
                            vm.paginationDocumentos = [vm.listaDocumentosLaudo];
                            vm.totalItems = vm.paginationDocumentos.length;
                        }
                    } else {
                        if (!vm.listaDocumentosLaudo) {
                            vm.listaDocumentosLaudo = [];
                        }
                        vm.paginacaoDocumentos();
                    }
                });

                vm.adicionarListaDocumentos = function () {
                    vm.formEnviado = true;

                    if (!vm.documento || !vm.documento.numeroDocumentoSei) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-necessario-informar-numero-documento-sei'));
                        return;
                    }

                    documentoSeiService.recuperarDocumentoSei(vm.documento.numeroDocumentoSei, vm.uf).then(function (resp) {
                        vm.documento = resp.data;

                        if (!vm.listaDocumentosLaudo) {
                            vm.listaDocumentosLaudo = [];
                        }

                        for (i = 0; i < vm.listaDocumentosLaudo.length; i++) {
                            if (vm.documento.numeroDocumentoSei === vm.listaDocumentosLaudo[i].numeroDocumentoSei) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-numero-documento-sei-informado-ja-adicionado-avaliação'));
                                vm.documento = {};
                                return;
                            }
                        }

                        if (vm.fileLimit && vm.fileLimit === 1) {
                            vm.listaDocumentosLaudo = angular.copy(vm.documento);
                            vm.paginationDocumentos = [vm.listaDocumentosLaudo];
                            vm.totalItems = vm.paginationDocumentos.length;
                            vm.documento = {};
                            return;
                        }

                        vm.listaDocumentosLaudo.push(vm.documento);
                        vm.formEnviado = false;

                        ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-documento-sei-adicionado-sucesso'));
                        vm.paginacaoDocumentos();
                        vm.documento = {};
                    }, function (erro) {
                        ComumFactory.mostrarMensagemError(erro.data.message);
                        vm.documento = {};
                    });



                };

                vm.removerDocumento = function ($index) {

                    var confirm = $mdDialog.confirm()
                        .title('Atenção!')
                        .textContent('Deseja remover este arquivo?')
                        .ok('Confirmar')
                        .cancel('Cancelar');

                    $mdDialog.show(confirm).then(function () {
                            vm.listaDocumentosLaudo.splice($index, 1);
                            vm.paginacaoDocumentos();
                    });
                };

                vm.paginacaoDocumentos = function () {
                    vm.totalItems = vm.listaDocumentosLaudo.length;

                    vm.paginationDocumentos = [];
                    vm.totalItems = vm.listaDocumentosLaudo.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationDocumentos = vm.listaDocumentosLaudo;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationDocumentos.push(vm.listaDocumentosLaudo[i]);
                            if (vm.paginationDocumentos.length === vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                vm.abrirDocumentoLinkAcesso = function (link) {
                    window.open(link);
                }

                vm.fileLimitValido = function () {
                    return !vm.fileLimit || (vm.fileLimit > (vm.listaDocumentosLaudo ? vm.listaDocumentosLaudo.length : 0));
                };

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                init();
            }
        };
    }
})();
(function () {

    directive.$inject = ["arquivoAvaliacaoService", "ComumFactory", "$filter", "$mdDialog", "$q"];
    angular.module('su-avaliacao')
        .directive('documentoLaudoAvaliacaoPadrao', directive);

    function directive(arquivoAvaliacaoService, ComumFactory, $filter, $mdDialog, $q) {
        return {
            restrict: 'EA',
            scope: {
                listaDocumentosLaudo: '=',
                listaDocumentosAux: '=?',
                detalharLaudoAvaliacao: '=',
                fileLimit: '=?',
                customNomeLabel: '=?',
                customButtonLabel: '=?',
                campoData: '=?',
                titulo: '=?',
                tipoFoto: '=?',
                anexoLaudoRelatorio: '=?',
                tipoAnexo: '=?',
                tipoValidacao: '=?',
                hint: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/documentoLaudoAvaliacaoPadrao.tmpl.html',
            link: function ($scope) {


                var vm = $scope;

                vm.arquivoTemp = {};

                var content_type_01 = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel',
                    'application/excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.oasis.opendocument.text', 'application/vnd.oasis.opendocument.spreadsheet', 'image/png', 'image/jpeg'];

                var content_type_02 = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/zip', 'application/x-compressed', 'application/x-zip-compressed',
                    'multipart/x-zip', 'application/excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.oasis.opendocument.text', 'application/vnd.oasis.opendocument.spreadsheet', 'image/png', 'image/jpeg'];

                var content_type_04 = ['application/pdf'];

                var CONTENT_TYPE_01 = 'CONTENT_TYPE_01';
                var CONTENT_TYPE_02 = 'CONTENT_TYPE_02';
                var CONTENT_TYPE_03 = 'CONTENT_TYPE_03';
                var CONTENT_TYPE_04 = 'CONTENT_TYPE_04';

                vm.documento = {};
                vm.isDocumento = false;
                vm.dataAtual = new Date();

                vm.paginationDocumentos = [];

                var init = function () {
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;
                };

                vm.adicionarArquivoDocumento = function (file) {

                    if (!file) {return;}

                    if (vm.tipoFoto) {
                        content_type_01 = ['image/jpeg', 'image/png', 'image/jpg'];
                    }

                    if ( vm.tipoValidacao === CONTENT_TYPE_04 && content_type_04.indexOf(file[0].type) !== -1
                        || (content_type_01.indexOf(file[0].type) !== -1 && (vm.tipoValidacao === CONTENT_TYPE_01 || !vm.tipoValidacao))
                        || (content_type_02.indexOf(file[0].type) !== -1 && vm.tipoValidacao === CONTENT_TYPE_02)
                        || (vm.tipoValidacao === CONTENT_TYPE_03)) {
                        arquivoAvaliacaoService.uploadArquivo(file[0]).then(
                            function (resp) {
                                vm.documento.arquivo = resp.data;
                            },
                            function error() {
                                ComumFactory.mostrarMensagemError($filter('translate')('arquivo-pdf-excede-tamanho'));
                            }
                        );
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-extensao-nao-permitida') + " " + "Efetue apenas o envio de extensão PDF" );
                    }
                };

                vm.$watch('listaDocumentosLaudo', function () {

                    if (vm.fileLimit && vm.fileLimit == 1) {
                        if (vm.listaDocumentosLaudo) {
                            vm.paginationDocumentos = [vm.listaDocumentosLaudo];
                            vm.totalItems = vm.paginationDocumentos.length;
                        }
                    } else {
                        if (!vm.listaDocumentosLaudo) {
                            vm.listaDocumentosLaudo = [];
                        }
                        vm.paginacaoDocumentos();
                    }
                });

                vm.adicionarListaDocumentos = function () {
                    vm.formEnviado = true;

                    if (!vm.documento || !vm.documento.nome || !vm.documento.arquivo) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }

                    if (vm.campoData && !vm.documento.dtArquivo) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }

                    if (vm.documento.dtArquivo > new Date(new Date().setHours(0, 0, 0, 0))) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-posterior-data-atual-arquivo'));
                        return;
                    }

                    if (!vm.listaDocumentosLaudo) {
                        vm.listaDocumentosLaudo = [];
                    }

                    for (i = 0; i < vm.listaDocumentosLaudo.length; i++) {
                        if (vm.documento.nome === vm.listaDocumentosLaudo[i].nome || (vm.documento.arquivo.nome === vm.listaDocumentosLaudo[i].arquivo.nome
                            && vm.documento.arquivo.extensao === vm.listaDocumentosLaudo[i].arquivo.extensao && vm.documento.arquivo.tamanho === vm.listaDocumentosLaudo[i].arquivo.tamanho)) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-inserido'));
                            arquivoAvaliacaoService.deletarArquivo(vm.documento.arquivo.id).then(function () {
                            });
                            vm.documento = {};
                            return;
                        }
                    }

                    if (vm.listaDocumentosAux) {
                        if (vm.listaDocumentosAux.length > 0 && !vm.listaDocumentosAux[0].numeroDocumentoSei) {
                            for (i = 0; i < vm.listaDocumentosAux.length; i++) {
                                if (vm.documento.nome === vm.listaDocumentosAux[i].nome || (vm.documento.arquivo.nome === vm.listaDocumentosAux[i].arquivo.nome
                                    && vm.documento.arquivo.extensao === vm.listaDocumentosAux[i].arquivo.extensao && vm.documento.arquivo.tamanho === vm.listaDocumentosAux[i].arquivo.tamanho)) {
                                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-inserido'));
                                    arquivoAvaliacaoService.deletarArquivo(vm.documento.arquivo.id).then(function () {
                                    });
                                    vm.documento = {};
                                    return;
                                }
                            }
                        }
                    }


                    if (vm.fileLimit && vm.fileLimit === 1) {
                        vm.listaDocumentosLaudo = angular.copy(vm.documento);
                        vm.paginationDocumentos = [vm.listaDocumentosLaudo];
                        vm.totalItems = vm.paginationDocumentos.length;
                        vm.documento = {};
                        return;
                    }

                    if (vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos) {
                        for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                            if (vm.anexoLaudoRelatorio.anexos[i].nome === vm.tipoAnexo) {
                                if (!vm.anexoLaudoRelatorio.anexos[i].filhos || !vm.anexoLaudoRelatorio.anexos[i].filhos.length) {
                                    vm.anexoLaudoRelatorio.anexos[i].filhos = [];
                                }

                                vm.anexoLaudoRelatorio.anexos[i].filhos.push({
                                    nome: vm.documento.nome,
                                    selecionado: false,
                                    ordem: vm.anexoLaudoRelatorio.anexos[i].filhos.length,
                                    arquivo: vm.documento.arquivo
                                });
                            }
                        }
                    }

                    vm.listaDocumentosLaudo.push(vm.documento);
                    vm.formEnviado = false;

                    vm.paginacaoDocumentos();
                    vm.documento = {};
                };

                vm.removerDocumento = function ($index, idArquivo) {

                    var confirm = $mdDialog.confirm()
                        .title('Atenção!')
                        .textContent('Deseja remover este arquivo?')
                        .ok('Confirmar')
                        .cancel('Cancelar');

                    $mdDialog.show(confirm).then(function () {

                        if (vm.anexoLaudoRelatorio && vm.anexoLaudoRelatorio.anexos) {
                            for (var i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                                if (vm.anexoLaudoRelatorio.anexos[i].nome === vm.tipoAnexo) {
                                    if (vm.anexoLaudoRelatorio.anexos[i].filhos) {
                                        for (var j = 0; j < vm.anexoLaudoRelatorio.anexos[i].filhos.length; j++) {
                                            if (vm.anexoLaudoRelatorio.anexos[i].filhos[j].arquivo.id == idArquivo) {
                                                vm.anexoLaudoRelatorio.anexos[i].filhos.splice(j, 1);
                                                break;
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        arquivoAvaliacaoService.deletarArquivo(idArquivo).then(function () {
                            vm.listaDocumentosLaudo.splice($index, 1);
                            vm.paginacaoDocumentos();
                        }, function () {
                            vm.listaDocumentosLaudo.splice($index, 1);
                            vm.paginacaoDocumentos();
                        });
                    });
                };

                vm.paginacaoDocumentos = function () {
                    vm.totalItems = vm.listaDocumentosLaudo.length;

                    vm.paginationDocumentos = [];
                    vm.totalItems = vm.listaDocumentosLaudo.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationDocumentos = vm.listaDocumentosLaudo;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationDocumentos.push(vm.listaDocumentosLaudo[i]);
                            if (vm.paginationDocumentos.length == vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                vm.fileLimitValido = function () {
                    return !vm.fileLimit || (vm.fileLimit > (vm.listaDocumentosLaudo ? vm.listaDocumentosLaudo.length : 0));
                };

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                init();
            }
        };
    }
})();
(function () {

    directive.$inject = ["arquivoAvaliacaoService", "ComumFactory", "$filter", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('documentoFonteAmostraPadrao', directive);

    function directive(arquivoAvaliacaoService, ComumFactory, $filter, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                listaDocumentos: '=',
                detalharAmostra: '=',
                fileLimit: '=?',
                customNomeLabel: '=?',
                customButtonLabel: '=?',
                titulo: '=?'
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/documentoFonteAmostra.tmpl.html',
            link: function ($scope) {


                var vm = $scope;

                vm.arquivoTemp = {};
                vm.listaTiposDocumentos = {
                    ANEXO: "Anexo",
                    LINK: "Link"
                };

                var content_type = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel',
                    'application/excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.oasis.opendocument.text ', 'application/vnd.oasis.opendocument.spreadsheet'];


                vm.documento = {};
                vm.isDocumento = false;
                vm.dataAtual = new Date();

                vm.paginationDocumentos = [];

                var init = function () {
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;
                };

                vm.adicionarArquivoDocumento = function (file) {

                    if (content_type.indexOf(file[0].type) != -1) {
                        arquivoAvaliacaoService.uploadArquivo(file[0]).then(function (resp) {
                            vm.documento.arquivo = resp.data;
                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-documento-formato-invalido'));
                    }

                };

                vm.$watch('listaDocumentos', function () {

                    if (vm.fileLimit && vm.fileLimit == 1) {
                        if (vm.listaDocumentos) {
                            vm.paginationDocumentos = [vm.listaDocumentos];
                            vm.totalItems = vm.paginationDocumentos.length;
                        }
                    } else {
                        if (!vm.listaDocumentos) {
                            vm.listaDocumentos = [];
                        }
                        vm.paginacaoDocumentos();
                    }
                });

                vm.adicionarListaDocumentos = function () {
                    vm.formEnviado = true;

                    if (vm.documento) {
                        if (vm.documento.tipo === 'ANEXO' && (!vm.documento.nome || !vm.documento.dtArquivo || !vm.documento.arquivo)) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                            return;
                        }
                        if (vm.documento.tipo === 'LINK' && !vm.documento.link) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                            return;
                        }
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }

                    if (!vm.listaDocumentos) {
                        vm.listaDocumentos = [];
                    }

                    if (vm.fileLimit && vm.fileLimit == 1) {
                        vm.listaDocumentos = angular.copy(vm.documento);
                        vm.paginationDocumentos = [vm.listaDocumentos];
                        vm.totalItems = vm.paginationDocumentos.length;
                        vm.documento = {};
                        return;
                    }

                    vm.listaDocumentos.push(vm.documento);
                    vm.formEnviado = false;

                    vm.paginacaoDocumentos();
                    vm.documento = {};
                };

                vm.removerDocumento = function ($index, idArquivo) {

                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja remover este arquivo?")
                        .ok("Confirmar")
                        .cancel("Cancelar");


                    $mdDialog.show(confirm).then(function () {
                        if (idArquivo) {
                            arquivoAvaliacaoService.deletarArquivo(idArquivo).then(function () {
                                vm.listaDocumentos.splice($index, 1);
                                vm.paginacaoDocumentos();
                            }, function () {
                                vm.listaDocumentos.splice($index, 1);
                                vm.paginacaoDocumentos();
                            });
                        } else {
                            vm.listaDocumentos.splice($index, 1);
                            vm.paginacaoDocumentos();
                        }
                    });

                };

                vm.paginacaoDocumentos = function () {
                    vm.totalItems = vm.listaDocumentos.length;

                    vm.paginationDocumentos = [];
                    vm.totalItems = vm.listaDocumentos.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationDocumentos = vm.listaDocumentos;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationDocumentos.push(vm.listaDocumentos[i]);
                            if (vm.paginationDocumentos.length == vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                vm.fileLimitValido = function () {
                    return !vm.fileLimit || (vm.fileLimit > (vm.listaDocumentos ? vm.listaDocumentos.length : 0));
                };

                vm.changeTipoFonte = function (tipoFonte) {
                    if(tipoFonte === 'LINK'){
                        vm.documento.nome = null;
                        vm.documento.dtArquivo = null;
                        vm.documento.arquivo = null;
                    }else{
                        vm.documento.link = null;
                        vm.documento.dtArquivo = null;
                    }
                };

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                init();
            }
        };
    }
})();
(function () {

    directive.$inject = ["$rootScope", "$state", "laudoAvaliacaoService", "$mdDialog", "$mdMedia", "ComumFactory", "$filter", "usuarioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "dominioAvaliacaoService"];
    angular.module('su-avaliacao')
        .directive('dadosRelatorioValorReferencia', directive);

    function directive($rootScope, $state, laudoAvaliacaoService,
                       $mdDialog, $mdMedia, ComumFactory, $filter,
                       usuarioAvaliacaoService, avaliacaoEscopoCompartilhadoService, dominioAvaliacaoService) {
        return {
            restrict: 'EA',
            scope: {
                rip: '=',
                uf: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarRelatorioValorReferencia.html',
            link: function ($scope) {

                var vm = $scope;

                vm.PENDENTE_HOMOLOGACAO = 'PENDENTE_HOMOLOGACAO';
                vm.CANCELADO = 'CANCELADO';

                var LAUDO_AVALIACAO = 'LAUDO_AVALIACAO';
                var LAUDO_AVALIACAO_AQUISICAO = 'LAUDO_AVALIACAO_AQUISICAO';
                var LAUDO_RELATORIO_VALOR_REFERENCIA = 'LAUDO_RELATORIO_VALOR_REFERENCIA';

                vm.LAUDO_RELATORIO_VALOR_REFERENCIA = 'LAUDO_RELATORIO_VALOR_REFERENCIA';

                vm.listaSituacoesLaudo = [];
                vm.avaliacoesLaudoAvaliacao = [];
                vm.situacaoLaudoAvaliacao = null;

                var init = function () {
                    vm.tipoAvaliacao = 'LAUDO_RELATORIO_VALOR_REFERENCIA';
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;

                    vm.consultarAvaliacoesLaudoAvaliacao();
                    buscarSituacoesLaudoAvaliacao();
                    buscarTiposElaboracaoLaudo();
                    buscarUsuarioLogado();

                    vm.ambienteDev = dominioAvaliacaoService.ehAmbienteDesenvolvimento();
                };

                var buscarTiposElaboracaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposElaboracaoLaudo().then(function (resp) {
                        vm.listaElaboracaoLaudo = resp.data;
                    });
                };

                vm.redirecionarTelaCadastrarRelatorioValorReferencia = function () {
                    $state.go('avaliacao.CADASTRAR_RELATORIO_VALOR_REFERENCIA', {
                        'rip': vm.rip
                    });
                };

                vm.redirecionarTelaEditarLaudoAvaliacao = function (idLaudo) {
                    $state.go('avaliacao.EDITAR_RELATORIO_VALOR_REFERENCIA', {
                        'rip': vm.rip,
                        'idLaudo': idLaudo
                    });
                };

                vm.elaborarLaudoRelatorioAvaliacao = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('isRipBloqueado', true);
                    $state.go('avaliacao.CADASTRAR_LAUDO_RELATORIO_AVALIACAO', {'rip': vm.rip});
                };

                vm.periodoValido = function (data) {
                    var dataFinal = new Date(data);
                    dataFinal.setFullYear(dataFinal.getFullYear() + 1);
                    return new Date() <= dataFinal;
                };

                vm.consultarAvaliacoesLaudoAvaliacao = function () {
                    var situacao = vm.situacaoLaudoAvaliacaoList;
                    if (situacao != null) {
                        for (var i = 0; i < vm.situacaoLaudoAvaliacaoList.length; i++) {
                            if (vm.situacaoLaudoAvaliacaoList[i] === 'TODOS') {
                                var removed = situacao.splice(i, 1);
                            }
                        }
                    }
                    laudoAvaliacaoService.buscarLaudosRelatorioAvaliacao(vm.tipoAvaliacao, situacao, vm.rip, (vm.registroInicial - 1), vm.tamanhoLimite)
                        .then(function (resp) {
                            vm.avaliacoesLaudoAvaliacao = resp.data.content;
                            vm.totalItems = resp.data.totalElements;
                        });
                };

                var buscarSituacoesLaudoAvaliacao = function () {

                    vm.listaSituacoesLaudo = {
                        TODOS: "Todos",
                        VALIDO: "Válido",
                        CANCELADO: "Cancelado",
                        EM_PREENCHIMENTO: "Cadastro em Preenchimento",
                        NAO_ACEITO_CHEFIA: "Não Aceito Pela Chefia",
                        PENDENTE_VALIDACAO_CHEFIA: "Pendente de Validação da Chefia",
                        REVALIDACAO_PREENCHIMENTO: "Revalidação em Preenchimento",
                        VIGENTE: "Vigente",
                        ATIVO_REVALIDADO: "Válido/Revalidado",
                        VIGENTE_REVALIDADO: "Vigente/Revalidado"
                    };

                };

                vm.selecionarTodos = function () {
                    if(!vm.isTodosSelecionados) {
                        for (var i = 0; i < vm.situacaoLaudoAvaliacaoList.length; i++) {
                            if (vm.situacaoLaudoAvaliacaoList[i] === 'TODOS') {
                                vm.situacaoLaudoAvaliacaoList = [
                                    "TODOS",
                                    "EM_PREENCHIMENTO",
                                    "REVALIDACAO_PREENCHIMENTO",
                                    "PENDENTE_VALIDACAO_CHEFIA",
                                    "VIGENTE",
                                    "VIGENTE_REVALIDADO",
                                    "VALIDO",
                                    "ATIVO_REVALIDADO",
                                    "CANCELADO",
                                    "NAO_ACEITO_CHEFIA"
                                ];
                                vm.isTodosSelecionados = true;
                                return;
                            }
                        }
                    }else {
                        vm.situacaoLaudoAvaliacaoList = [];
                        vm.isTodosSelecionados = false;
                    }
                };

                vm.formatarTipoAvaliacaoLaudo = function (tipoAvaliacao) {
                    if (LAUDO_AVALIACAO == tipoAvaliacao) {
                        return 'Laudo';
                    } else if (LAUDO_AVALIACAO_AQUISICAO == tipoAvaliacao) {
                        return 'Aquisição';
                    } else if (LAUDO_RELATORIO_VALOR_REFERENCIA == tipoAvaliacao) {
                        return 'Relatório';
                    }
                };

                vm.redirecionarDetalharLaudo = function (idLaudo) {
                    $state.go('avaliacao.DETALHAR_RELATORIO_VALOR_REFERENCIA', {
                        'rip': vm.rip,
                        'idLaudo': idLaudo
                    });
                };

                vm.visualizarHistorico = function (idLaudo) {
                    laudoAvaliacaoService.buscarHistoricoAcoes(idLaudo).then(function (resp) {
                        var idAcaoAvaliacao = resp.data;
                        $state.go('avaliacao.VISUALIZAR_HISTORICO_ACOES_AVALIACAO', {
                            idAcaoAvaliacao: idAcaoAvaliacao
                        });
                    });
                }

                vm.usuarioLogadoHomologador = function (homologadores) {
                    for (var i = 0; i < homologadores.length; i++) {
                        if (homologadores[i].cpf === vm.usuarioLogado.cpf) {
                            if(homologadores[i].isAssinado){
                                return false;
                            }else {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                var buscarUsuarioLogado = function () {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                    });
                };

                vm.redirecionarConsultarAcoesAvaliacao = function (laudoAvaliacao) {

                    vm.filtroConsulta = {};
                    vm.filtroConsulta.ripImovel = vm.rip;

                    if (usuarioAvaliacaoService.mesmosUsuarios(laudoAvaliacao.acaoPendenteDTO, vm.usuarioLogado)){
                        return;
                    }
                    vm.filtroConsulta.tipoAcaoAvaliacaoList = [laudoAvaliacao.acaoPendenteDTO.tipoAcaoAvaliacao];

                    vm.filtroConsulta.tipoStatusAcao = "PENDENTE";

                    vm.filtroConsulta.tipoCargoList = ["CHEFIA"];

                    avaliacaoEscopoCompartilhadoService.setObjetos('filtroConsulta', vm.filtroConsulta);
                    $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');
                };

                vm.removerRascunhoLaudoAvaliacao = function (idLaudo) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja excluir o rascunho de Laudo Relatório Valor Referência?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        laudoAvaliacaoService.removerRascunhoLaudoAvaliacao(idLaudo).then(function (resp) {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-rascunho-removido'));
                            vm.consultarAvaliacoesLaudoAvaliacao();
                        });
                    }, function () {

                    });
                };

                vm.excluirLaudoAvaliacao = function (idLaudo) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja excluir este relatório?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        laudoAvaliacaoService.excluirLaudoAvaliacao(idLaudo).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('label-relatorio-excluido-com-sucesso'));
                            vm.consultarAvaliacoesLaudoAvaliacao();
                        });
                    }, function () {

                    });
                };

                vm.excluirPendenciaLaudoAvaliacao = function (idLaudo) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja excluir essa pendência?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        laudoAvaliacaoService.excluirPendenciaLaudoAvaliacao(idLaudo).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('label-pendencia-excluida-com-sucesso'));
                            vm.consultarAvaliacoesLaudoAvaliacao();
                        });
                    }, function () {

                    });
                };

                vm.cancelarLaudoAvaliacao = function (idLaudo) {
                    var rip = vm.rip;
                    laudoAvaliacaoService.verificarCancelarLaudoAvaliacao(rip).then(function (resp) {
                        vm.podeCancelar = resp.data;

                        if (!vm.podeCancelar) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-erro-cancelar-laudo'));
                            return;
                        }
                        
                        $state.go('avaliacao.CANCELAR_LAUDO_AVALIACAO', {
                            rip: rip,
                            idLaudo: idLaudo
                        });
                    });

                };

                vm.redirecionarRevalidarLaudoAvaliacao = function (idLaudo) {
                    $state.go('avaliacao.REVALIDAR_LAUDO_AVALIACAO', {
                        idLaudo: idLaudo
                    });
                };

                vm.exibirCancelar = function (situacao) {
                    var situacoesPermitidas = [
                        'VIGENTE',
                        'VIGENTE_REVALIDADO',
                        'VALIDO',
                        'ATIVO_REVALIDADO'
                    ];

                    return situacoesPermitidas.includes(situacao);
                };


                vm.verificarLaudosRevalidados = function (laudoAvaliacao) {
                    return laudoAvaliacaoService.verificarLaudosRevalidados(laudoAvaliacao);
                }

                init();
            }
        };
    }

})();

(function () {

    directive.$inject = ["$state", "laudoAvaliacaoService", "$mdDialog", "$mdMedia", "formularioAvaliacaoService", "ComumFactory", "$filter", "comumAvaliacaoService", "usuarioAvaliacaoService", "avaliacaoEscopoCompartilhadoService"];
    angular.module('su-avaliacao')
        .directive('dadosLaudoAvaliacaoLocacao', directive);

    function directive($state, laudoAvaliacaoService,
                       $mdDialog, $mdMedia, formularioAvaliacaoService, ComumFactory, $filter, comumAvaliacaoService, usuarioAvaliacaoService, avaliacaoEscopoCompartilhadoService) {
        return {
            restrict: 'EA',
            scope: {
                rip: '=',
                uf: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarLaudoAvaliacaoLocacao.html',
            link: function ($scope) {

                var vm = $scope;

                vm.PENDENTE_HOMOLOGACAO = 'PENDENTE_HOMOLOGACAO';
                vm.EM_PROCESSO_REVALIDACAO = 'EM_PROCESSO_REVALIDACAO';
                vm.CANCELADO = 'CANCELADO';

                var LAUDO_AVALIACAO = 'LAUDO_AVALIACAO';
                var LAUDO_AVALIACAO_AQUISICAO = 'LAUDO_AVALIACAO_AQUISICAO';
                var LAUDO_RELATORIO_VALOR_REFERENCIA = 'LAUDO_RELATORIO_VALOR_REFERENCIA';

                vm.LAUDO_AVALIACAO = 'LAUDO_AVALIACAO';
                vm.LAUDO_RELATORIO_VALOR_REFERENCIA = 'LAUDO_RELATORIO_VALOR_REFERENCIA';
                vm.LAUDO_AVALIACAO_LOCACAO = 'LAUDO_AVALIACAO_LOCACAO';

                vm.listaSituacoesLaudo = [];
                vm.avaliacoesLaudoAvaliacao = [];
                vm.situacaoLaudoAvaliacaoList = null;
                vm.isTodosSelecionados = false;

                var init = function () {
                    vm.tipoAvaliacao = 'LAUDO_AVALIACAO_LOCACAO';
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;

                    vm.consultarAvaliacoesLaudoAvaliacao();
                    buscarSituacoesLaudoAvaliacao();
                    buscarTiposElaboracaoLaudo();
                    buscarUsuarioLogado();
                    verificarLaudoRevalidacao();
                };

                var buscarTiposElaboracaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposElaboracaoLaudo().then(function (resp) {
                        vm.listaElaboracaoLaudo = resp.data;
                    });
                };

                vm.redirecionarTelaCadastrarLaudoAvaliacao = function () {
                    $state.go('avaliacao.CADASTRAR_LAUDO_AVALIACAO_LOCACAO', {
                        'rip': vm.rip
                    });
                };

                vm.redirecionarTelaEditarLaudoAvaliacao = function (idLaudo) {
                    $state.go('avaliacao.EDITAR_LAUDO_AVALIACAO_LOCACAO', {
                        'rip': vm.rip,
                        'idLaudo': idLaudo
                    });
                };

                vm.elaborarLaudoRelatorioAvaliacao = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('isRipBloqueado', true);
                    $state.go('avaliacao.CADASTRAR_LAUDO_RELATORIO_AVALIACAO', {'rip': vm.rip});
                };

                vm.periodoValido = function (data) {
                    var dataFinal = new Date(data);
                    dataFinal.setFullYear(dataFinal.getFullYear() + 1);
                    return new Date() <= dataFinal;
                };

                vm.consultarAvaliacoesLaudoAvaliacao = function () {
                    var situacao = vm.situacaoLaudoAvaliacaoList;
                    if (situacao != null) {
                        for (var i = 0; i < vm.situacaoLaudoAvaliacaoList.length; i++) {
                            if (vm.situacaoLaudoAvaliacaoList[i] === 'TODOS') {
                                situacao = null;
                            }
                        }
                    }
                    laudoAvaliacaoService.buscarLaudosRelatorioAvaliacao(vm.tipoAvaliacao, situacao, vm.rip, (vm.registroInicial - 1), vm.tamanhoLimite)
                        .then(function (resp) {
                            vm.avaliacoesLaudoAvaliacao = resp.data.content;
                            vm.totalItems = resp.data.totalElements;
                        });
                };

                var buscarSituacoesLaudoAvaliacao = function () {

                    vm.listaSituacoesLaudo = {
                        TODOS: "Todos",
                        VALIDO: "Válido",
                        ATIVO_REVALIDADO: "Válido/Revalidado",
                        EM_PREENCHIMENTO: "Cadastro em Preenchimento",
                        CANCELADO: "Cancelado",
                        EM_PROCESSO_HOMOLOGACAO: "Em Processo de Homologação",
                        EM_PROCESSO_REVALIDACAO: "Em Processo de Revalidação",
                        NAO_ACEITO_CHEFIA: "Não Aceito pela Chefia",
                        NAO_ACEITO_TECNICO: "Não Aceito pelo Técnico",
                        NAO_HOMOLOGADO: "Não Homologado",
                        PENDENTE_HOMOLOGACAO: "Pendente de Assinatura da NT de Homologação",
                        PENDENTE_VALIDACAO_CHEFIA: "Pendente de Validação da Chefia",
                        PENDENTE_VALIDACAO_TECNICO: "Pendente de Validação do Técnico",
                        REVALIDACAO_PREENCHIMENTO: "Revalidação em Preenchimento",
                        VIGENTE: "Vigente",
                        VIGENTE_REVALIDADO: "Vigente/Revalidado"
                    };
                };

                vm.selecionarTodos = function () {
                    if(!vm.isTodosSelecionados) {
                        for (var i = 0; i < vm.situacaoLaudoAvaliacaoList.length; i++) {
                            if (vm.situacaoLaudoAvaliacaoList[i] === 'TODOS') {
                                vm.situacaoLaudoAvaliacaoList = [
                                    "TODOS",
                                    "EM_PREENCHIMENTO",
                                    "REVALIDACAO_PREENCHIMENTO",
                                    "PENDENTE_HOMOLOGACAO",
                                    "EM_PROCESSO_HOMOLOGACAO",
                                    "EM_PROCESSO_REVALIDACAO",
                                    "PENDENTE_VALIDACAO_CHEFIA",
                                    "PENDENTE_VALIDACAO_TECNICO",
                                    "VIGENTE",
                                    "VIGENTE_REVALIDADO",
                                    "VALIDO",
                                    "ATIVO_REVALIDADO",
                                    "CANCELADO",
                                    "NAO_ACEITO_CHEFIA",
                                    "NAO_ACEITO_TECNICO",
                                    "NAO_HOMOLOGADO"
                                ];
                                vm.isTodosSelecionados = true;
                                return;
                            }
                        }
                    }else {
                        vm.situacaoLaudoAvaliacaoList = [];
                        vm.isTodosSelecionados = false;
                    }
                };

                var verificarLaudoRevalidacao = function () {
                    laudoAvaliacaoService.verificarLaudoRevalidacao(vm.rip, vm.tipoAvaliacao).then(function (resp) {
                        vm.laudoRevalidacao = resp.data;
                    });
                };

                vm.formatarTipoAvaliacaoLaudo = function (tipoAvaliacao) {
                    if (LAUDO_AVALIACAO == tipoAvaliacao) {
                        return 'Laudo';
                    } else if (LAUDO_AVALIACAO_AQUISICAO == tipoAvaliacao) {
                        return 'Aquisição';
                    } else if (LAUDO_RELATORIO_VALOR_REFERENCIA == tipoAvaliacao) {
                        return 'Relatório';
                    }
                };

                vm.redirecionarDetalharLaudo = function (idLaudo) {
                    $state.go('avaliacao.DETALHAR_LAUDO_AVALIACAO_LOCACAO', {
                        'rip': vm.rip,
                        'idLaudo': idLaudo
                    });
                };

                vm.redirecionarRevalidarLaudoAvaliacao = function (idLaudo) {
                    $state.go('avaliacao.REVALIDAR_LAUDO_AVALIACAO', {
                        idLaudo: idLaudo
                    });
                };

                vm.redirecionarAssinarNotaTecnicaLaudoAvaliacao = function (idLaudo) {
                    $state.go('avaliacao.ASSINAR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO', {
                        idLaudo: idLaudo
                    })
                };

                vm.redirecionarAssinarRevalidacaoLaudoAvaliacao = function (idLaudo) {
                    $state.go('avaliacao.ASSINAR_REVALIDAR_LAUDO_AVALIACAO', {
                        idLaudo: idLaudo
                    });
                };

                vm.visualizarHistorico = function (idLaudo) {
                    laudoAvaliacaoService.buscarHistoricoAcoes(idLaudo).then(function (resp) {
                        var idAcaoAvaliacao = resp.data;
                        $state.go('avaliacao.VISUALIZAR_HISTORICO_ACOES_AVALIACAO', {
                            idAcaoAvaliacao: idAcaoAvaliacao
                        });
                    });
                }

                vm.usuarioLogadoHomologador = function (homologadores) {
                    for (var i = 0; i < homologadores.length; i++) {
                        if (homologadores[i].cpf === vm.usuarioLogado.cpf) {
                            if(homologadores[i].isAssinado){
                                return false;
                            }else {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                var buscarUsuarioLogado = function () {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                    });
                };

                vm.redirecionarConsultarAcoesAvaliacao = function (laudoAvaliacao) {
                    vm.filtroConsulta = {};
                    vm.filtroConsulta.ripImovel = vm.rip;
                    if (laudoAvaliacao.revalidacaoLaudoAvaliacao != null) {
                        vm.filtroConsulta.tipoAcaoAvaliacaoList = ["REVALIDACAO_LAUDO"];
                    } else {
                        vm.filtroConsulta.tipoAcaoAvaliacaoList = ["AVALIACAO_LAUDO_LOCACAO"];
                    }
                    vm.filtroConsulta.tipoStatusAcao = "PENDENTE";

                    avaliacaoEscopoCompartilhadoService.setObjetos('filtroConsulta', vm.filtroConsulta);
                    $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');
                };

                vm.removerRascunhoLaudoAvaliacao = function (idLaudo) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja excluir o rascunho de Laudo Avaliação")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        laudoAvaliacaoService.removerRascunhoLaudoAvaliacao(idLaudo).then(function (resp) {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-rascunho-removido'));
                            vm.consultarAvaliacoesLaudoAvaliacao();
                        });
                    }, function () {

                    });
                };

                vm.excluirLaudoAvaliacao = function (idLaudo) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja excluir este laudo?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        laudoAvaliacaoService.excluirLaudoAvaliacao(idLaudo).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('label-laudo-excluido-com-sucesso'));
                            vm.consultarAvaliacoesLaudoAvaliacao();
                        });
                    }, function () {

                    });
                };

                vm.cancelarLaudoAvaliacao = function (idLaudo) {
                    var rip = vm.rip;
                    $state.go('avaliacao.CANCELAR_LAUDO_AVALIACAO', {
                        rip: rip,
                        idLaudo: idLaudo
                    });

                };

                init();
            }
        };
    }

})();

(function () {

    directive.$inject = ["$rootScope", "$state", "laudoAvaliacaoService", "$mdDialog", "$mdMedia", "ComumFactory", "$filter", "usuarioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "dominioAvaliacaoService"];
    angular.module('su-avaliacao')
        .directive('dadosLaudoAvaliacao', directive);

    function directive($rootScope, $state, laudoAvaliacaoService,
                       $mdDialog, $mdMedia, ComumFactory, $filter,
                       usuarioAvaliacaoService, avaliacaoEscopoCompartilhadoService,
                       dominioAvaliacaoService) {
        return {
            restrict: 'EA',
            scope: {
                rip: '=',
                uf: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarLaudoAvaliacao.html',
            link: function ($scope) {

                var vm = $scope;

                vm.PENDENTE_HOMOLOGACAO = 'PENDENTE_HOMOLOGACAO';
                vm.CANCELADO = 'CANCELADO';

                var LAUDO_AVALIACAO = 'LAUDO_AVALIACAO';
                var LAUDO_AVALIACAO_AQUISICAO = 'LAUDO_AVALIACAO_AQUISICAO';
                var LAUDO_RELATORIO_VALOR_REFERENCIA = 'LAUDO_RELATORIO_VALOR_REFERENCIA';

                vm.LAUDO_RELATORIO_VALOR_REFERENCIA = 'LAUDO_RELATORIO_VALOR_REFERENCIA';

                vm.listaSituacoesLaudo = [];
                vm.avaliacoesLaudoAvaliacao = [];
                vm.situacaoLaudoAvaliacaoList = null;
                vm.isTodosSelecionados = false;

                var init = function () {
                    vm.tipoAvaliacao = 'LAUDO_AVALIACAO';
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;

                    vm.consultarAvaliacoesLaudoAvaliacao();
                    buscarSituacoesLaudoAvaliacao();
                    buscarTiposElaboracaoLaudo();
                    buscarUsuarioLogado();

                    vm.ambienteDev = dominioAvaliacaoService.ehAmbienteDesenvolvimento();
                };

                var buscarTiposElaboracaoLaudo = function () {
                    laudoAvaliacaoService.buscarTiposElaboracaoLaudo().then(function (resp) {
                        vm.listaElaboracaoLaudo = resp.data;
                    });
                };

                vm.redirecionarTelaCadastrarLaudoAvaliacao = function () {
                    $state.go('avaliacao.CADASTRAR_LAUDO_AVALIACAO', {
                        'rip': vm.rip
                    });
                };

                vm.redirecionarTelaEditarLaudoAvaliacao = function (idLaudo) {
                    $state.go('avaliacao.EDITAR_LAUDO_AVALIACAO', {
                        'rip': vm.rip,
                        'idLaudo': idLaudo
                    });
                };

                vm.elaborarLaudoRelatorioAvaliacao = function () {
                    avaliacaoEscopoCompartilhadoService.setObjetos('isRipBloqueado', true);
                    $state.go('avaliacao.CADASTRAR_LAUDO_RELATORIO_AVALIACAO', {'rip': vm.rip});
                };

                vm.periodoValido = function (data) {
                    var dataFinal = new Date(data);
                    dataFinal.setFullYear(dataFinal.getFullYear() + 1);
                    return new Date() <= dataFinal;
                };

                vm.consultarAvaliacoesLaudoAvaliacao = function () {
                    var situacao = vm.situacaoLaudoAvaliacaoList;
                    if (situacao != null) {
                        for (var i = 0; i < vm.situacaoLaudoAvaliacaoList.length; i++) {
                            if (vm.situacaoLaudoAvaliacaoList[i] === 'TODOS') {
                                var removed = situacao.splice(i, 1);
                            }
                        }
                    }
                    laudoAvaliacaoService.buscarLaudosRelatorioAvaliacao(vm.tipoAvaliacao, situacao, vm.rip, (vm.registroInicial - 1), vm.tamanhoLimite)
                        .then(function (resp) {
                            vm.avaliacoesLaudoAvaliacao = resp.data.content;
                            vm.totalItems = resp.data.totalElements;
                        }, function(response) { // error has occured
                            ComumFactory.mostrarMensagemSucesso(response.message);
                        });
                };

                var buscarSituacoesLaudoAvaliacao = function () {

                    vm.listaSituacoesLaudo = {
                        TODOS: "Todos",
                        VALIDO: "Válido",
                        ATIVO_REVALIDADO: "Válido/Revalidado",
                        EM_PREENCHIMENTO: "Cadastro em Preenchimento",
                        CANCELADO: "Cancelado",
                        NAO_ACEITO_CHEFIA: "Não Aceito Pela Chefia",
                        PENDENTE_VALIDACAO_CHEFIA: "Pendente de Validação da Chefia",
                        REVALIDACAO_PREENCHIMENTO: "Revalidação em Preenchimento",
                        VIGENTE: "Vigente",
                        VIGENTE_REVALIDADO: "Vigente/Revalidado"
                    };
                }

                vm.selecionarTodos = function () {
                    if (!vm.isTodosSelecionados) {
                        for (var i = 0; i < vm.situacaoLaudoAvaliacaoList.length; i++) {
                            if (vm.situacaoLaudoAvaliacaoList[i] === 'TODOS') {
                                vm.situacaoLaudoAvaliacaoList = [
                                    "TODOS",
                                    "EM_PREENCHIMENTO",
                                    "REVALIDACAO_PREENCHIMENTO",
                                    "PENDENTE_VALIDACAO_CHEFIA",
                                    "VIGENTE",
                                    "VIGENTE_REVALIDADO",
                                    "VALIDO",
                                    "ATIVO_REVALIDADO",
                                    "CANCELADO",
                                    "NAO_ACEITO_CHEFIA"
                                ];
                                vm.isTodosSelecionados = true;
                                return;
                            }
                        }
                    } else {
                        vm.situacaoLaudoAvaliacaoList = [];
                        vm.isTodosSelecionados = false;
                    }
                };

                vm.formatarTipoAvaliacaoLaudo = function (tipoAvaliacao) {
                    if (LAUDO_AVALIACAO == tipoAvaliacao) {
                        return 'Laudo';
                    } else if (LAUDO_AVALIACAO_AQUISICAO == tipoAvaliacao) {
                        return 'Aquisição';
                    } else if (LAUDO_RELATORIO_VALOR_REFERENCIA == tipoAvaliacao) {
                        return 'Relatório';
                    }
                };

                vm.redirecionarDetalharLaudo = function (idLaudo) {
                    $state.go('avaliacao.DETALHAR_LAUDO_AVALIACAO', {
                        'rip': vm.rip,
                        'idLaudo': idLaudo
                    });
                };

                vm.redirecionarRevalidarLaudoAvaliacao = function (idLaudo) {
                    $state.go('avaliacao.REVALIDAR_LAUDO_AVALIACAO', {
                        idLaudo: idLaudo
                    });
                };

                vm.redirecionarAssinarNotaTecnicaLaudoAvaliacao = function (idLaudo) {
                    $state.go('avaliacao.ASSINAR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO', {
                        idLaudo: idLaudo
                    })
                };

                vm.visualizarHistorico = function (idLaudo) {
                    laudoAvaliacaoService.buscarHistoricoAcoes(idLaudo).then(function (resp) {
                        var idAcaoAvaliacao = resp.data;
                        $state.go('avaliacao.VISUALIZAR_HISTORICO_ACOES_AVALIACAO', {
                            idAcaoAvaliacao: idAcaoAvaliacao
                        });
                    });
                }

                vm.usuarioLogadoHomologador = function (homologadores) {
                    if (!Array.isArray(homologadores))
                        return false;

                    for (var i = 0; i < homologadores.length; i++) {
                        if (homologadores[i].cpf === vm.usuarioLogado.cpf) {
                            if (homologadores[i].isAssinado) {
                                return false;
                            } else {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                var buscarUsuarioLogado = function () {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                    });
                };

                vm.redirecionarConsultarAcoesAvaliacao = function (laudoAvaliacao) {

                    vm.filtroConsulta = {};
                    vm.filtroConsulta.ripImovel = vm.rip;

                    if (usuarioAvaliacaoService.mesmosUsuarios(laudoAvaliacao.acaoPendenteDTO, vm.usuarioLogado)){
                        return;
                    }
                    vm.filtroConsulta.tipoAcaoAvaliacaoList = [laudoAvaliacao.acaoPendenteDTO.tipoAcaoAvaliacao];

                    vm.filtroConsulta.tipoStatusAcao = "PENDENTE";
                    vm.filtroConsulta.tipoCargoList = ["CHEFIA"];

                    avaliacaoEscopoCompartilhadoService.setObjetos('filtroConsulta', vm.filtroConsulta);
                    $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');

                };

                vm.removerRascunhoLaudoAvaliacao = function (idLaudo) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja remover este rascunho?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        laudoAvaliacaoService.removerRascunhoLaudoAvaliacao(idLaudo).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-rascunho-removido'));
                            vm.consultarAvaliacoesLaudoAvaliacao();
                        });
                    }, function () {

                    });
                };

                vm.excluirLaudoAvaliacao = function (idLaudo) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja excluir a pendência de validação?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        laudoAvaliacaoService.excluirLaudoAvaliacao(idLaudo).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('label-laudo-excluido-com-sucesso'));
                            vm.consultarAvaliacoesLaudoAvaliacao();
                        });
                    }, function () {

                    });
                };

                vm.excluirPendenciaLaudoAvaliacao = function (idLaudo) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja excluir essa pendência?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        laudoAvaliacaoService.excluirPendenciaLaudoAvaliacao(idLaudo).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('label-pendencia-excluida-com-sucesso'));
                            vm.consultarAvaliacoesLaudoAvaliacao();
                        });
                    }, function () {

                    });
                };

                vm.cancelarLaudoAvaliacao = function (idLaudo) {
                    var rip = vm.rip;
                    laudoAvaliacaoService.verificarCancelarLaudoAvaliacao(rip).then(function (resp) {
                        vm.podeCancelar = resp.data;

                        if (!vm.podeCancelar) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-erro-cancelar-laudo'));
                            return;
                        }
                        
                        $state.go('avaliacao.CANCELAR_LAUDO_AVALIACAO', {
                            rip: rip,
                            idLaudo: idLaudo
                        });
                    });

                };

                vm.verificarLaudosRevalidados = function (laudoAvaliacao) {
                    return laudoAvaliacaoService.verificarLaudosRevalidados(laudoAvaliacao);
                }

                init();
            }
        };
    }

})();

(function () {

    directive.$inject = ["$mdDialog", "ComumFactory", "$filter", "amostraService"];
    angular.module('su-avaliacao')
        .directive('dadosComplementaresAmostra', directive);

    function directive($mdDialog, ComumFactory, $filter, amostraService) {
        return {
            restrict: 'EA',
            scope: {
                dadosComplementares: '=',
                detalharAmostra: '=',
                gerarPlanilha: '=',
                listaTiposDadosComplementares: '=',
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/dadosComplementaresAmostra.tmpl.html',
            link: function ($scope) {

                var vm = $scope;
                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;
                vm.paginationVariaveis = [];

                vm.editar = {};
                vm.editar.buttonShow = false;

                var init = function () {
                    vm.listaTiposDadosComplementaresBackup = angular.copy(vm.listaTiposDadosComplementares);
                    vm.paginacaoDadosComplementares();
                };


                vm.adicionarDadoComplementar = function ($index) {
                    if (vm.dadoComplementar && vm.dadoComplementar.tipoDadoComplementar) {
                        if (vm.dadoComplementar.tipoDadoComplementar.nome === 'Outro' && !vm.dadoComplementar.descricaoOutros) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                            return
                        }

                        if (!vm.dadosComplementares) {
                            vm.dadosComplementares = [];
                        }

                        vm.dadosComplementares.push(vm.dadoComplementar);

                        for (var i = 0; i < vm.listaTiposDadosComplementares.length; i++) {
                            for (var j = 0; j < vm.dadosComplementares.length; j++) {
                                if (vm.listaTiposDadosComplementares[i].nome === vm.dadosComplementares[j].tipoDadoComplementar.nome) {
                                    if(vm.listaTiposDadosComplementares[i].nome !== "Outro") {
                                        vm.listaTiposDadosComplementares.splice(i, 1);
                                    }
                                }
                            }
                        }
                        vm.dadoComplementar = {};
                        vm.paginacaoDadosComplementares();
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                    }
                };


                vm.alterarDadoComplementar = function ($index) {
                    vm.dadoComplementar = angular.copy(vm.paginationDadosComplementares[$index]);
                    if(vm.dadoComplementar.tipoDadoComplementar.nome !== "Outro") {
                        vm.listaTiposDadosComplementares.push(vm.dadoComplementar.tipoDadoComplementar);
                    }
                    vm.editar.index = $index;
                    vm.editar.buttonShow = true;
                };

                vm.gravarAlteracaoDadoComplementar = function () {
                    if (vm.dadoComplementar.tipoDadoComplementar.nome !== 'Outro' || (vm.dadoComplementar.tipoDadoComplementar.nome === 'Outro' && vm.dadoComplementar.descricaoOutros)) {
                        vm.dadosComplementares.splice(vm.editar.index, 1);
                        vm.dadosComplementares.push(vm.dadoComplementar);

                        for (var i = 0; i < vm.listaTiposDadosComplementares.length; i++) {
                            for (var j = 0; j < vm.dadosComplementares.length; j++) {
                                if (vm.listaTiposDadosComplementares[i].nome === vm.dadosComplementares[j].tipoDadoComplementar.nome) {
                                    if(vm.listaTiposDadosComplementares[i].nome !== "Outro") {
                                        vm.listaTiposDadosComplementares.splice(i, 1);
                                    }
                                }
                            }
                        }

                        vm.dadoComplementar = {};
                        vm.paginacaoDadosComplementares();
                        vm.editar.index = null;
                        vm.editar.buttonShow = false;
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                    }
                };


                vm.removerDadoComplementar = function ($index) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent($filter('translate')('label-remover-dado-complementar'))
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        vm.dadosComplementares.splice($index, 1);
                        vm.listaTiposDadosComplementares = angular.copy(vm.listaTiposDadosComplementaresBackup);
                        for (var i = 0; i < vm.listaTiposDadosComplementares.length; i++) {
                            for (var j = 0; j < vm.dadosComplementares.length; j++) {
                                if (vm.listaTiposDadosComplementares[i].nome === vm.dadosComplementares[j].tipoDadoComplementar.nome) {
                                    if(vm.listaTiposDadosComplementares[i].nome !== "Outro") {
                                        vm.listaTiposDadosComplementares.splice(i, 1);
                                    }
                                }
                            }
                        }
                        vm.paginacaoDadosComplementares();
                    });
                };

                vm.paginacaoDadosComplementares = function () {
                    vm.paginationDadosComplementares = [];
                    vm.totalItems = vm.dadosComplementares.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        vm.paginationDadosComplementares = vm.dadosComplementares;
                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationDadosComplementares.push(vm.dadosComplementares[i]);
                            if (vm.paginationDadosComplementares.length === vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };


                init();
            }
        };
    };

})();
/**
 * Created by tawan-souza on 16/03/17.
 */
(function () {

    directive.$inject = ["dominioPlenoService"];
    angular.module('su-avaliacao')
        .directive('apresentarValorDominioPleno', directive);

    function directive(dominioPlenoService) {
        return {
            restrict: 'EA',
            scope: {
                rip: '=',
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/apresentarValorDominioPleno.html',
            link: function ($scope) {

                var vm = $scope;

                var init = function () {
                    vm.listaValoresDominioPleno = [];
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;
                    vm.buscarValorDominioPleno();
                };

                vm.buscarValorDominioPleno = function () {
                    dominioPlenoService.buscarValorDominioPleno(vm.rip, (vm.registroInicial - 1), vm.tamanhoLimite)
                        .then(function (resp) {
                            vm.listaValoresDominioPleno = resp.data.content;
                            vm.totalItems = resp.data.totalElements;
                        });
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["$mdDialog", "$mdMedia", "pvgSpuService", "comumAvaliacaoService", "ComumFactory", "$filter", "$state", "formularioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "usuarioAvaliacaoService"];
    angular.module('su-avaliacao')
        .directive('adicionarAvaliacaoPvgSpu', directive);

    function directive ($mdDialog, $mdMedia, pvgSpuService, comumAvaliacaoService, ComumFactory, $filter, $state,
                        formularioAvaliacaoService, avaliacaoEscopoCompartilhadoService, usuarioAvaliacaoService) {
        return {
            restrict: 'EA',
            scope: {
                rip: '=',
                imovel: '=',
                areaTerreno: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarAvaliacaoPvgSpu.html',
            link: function ($scope) {

                var vm = $scope;

                var init = function() {
                    buscarUsuarioLogado();
                    vm.historicoAvaliacoesPvgsSpu = [];
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;

                    vm.showAvaliacoesTerrenos = false;
                    vm.showDesvincularPvgSpu = false;

                    vm.pesquisarStatusPvg();
                    vm.buscarHistoricoPvgSpu();
                    vm.verficarAvaliacoesPvgSpu();
                    pesquisarTrechosSelecionados();
                };

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                vm.buscarHistoricoPvgSpu = function() {
                    pvgSpuService.buscarHistorioAvaliacaoPvgSpu(vm.rip, (vm.registroInicial - 1), vm.tamanhoLimite).then(function(resp) {
                            vm.historicoAvaliacoesPvgsSpu = resp.data.content;
                            vm.totalItems = resp.data.totalElements;
                    });
                };

                vm.verficarAvaliacoesPvgSpu = function() {
                    pvgSpuService.verficarAvaliacoesPvgSpu(vm.rip, (vm.registroInicial - 1), vm.tamanhoLimite).then(function(resp) {
                        vm.showAvaliacoesTerrenos  = resp.data.totalElements > 0;

                    });
                };

                vm.pesquisarStatusPvg = function() {
                    return comumAvaliacaoService.consultarStatusAvaliacao().then(function (resp) {
                        vm.listaStatusAvaliacao = resp.data;
                    });
                };

                vm.cancelarAvaliacaoSpu = function (ev,  id) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    $mdDialog.show({
                        controller: ["$scope", function ($scope) {

                            $scope.id = id;
                            $scope.selecioneMotivo = '';
                            $scope.pvgSpu = {};
                            $scope.data = new Date();
                            $scope.justificativa = '';

                            var init = function () {

                                $scope.titulo = $filter('translate')('title-cancelar-pvg_spu');

                                comumAvaliacaoService.usuarioLogado()
                                    .then(function (resp) {
                                        $scope.nomeResponsavel = resp.data;
                                });

                                comumAvaliacaoService.consultarMotivosCancelar()
                                    .then(function(resp) {

                                        $scope.motivosCancelar = resp.data;
                                    });
                            };

                            var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                            $scope.validarFormulario = formularioServiceInstance.validarFormulario;

                            $scope.fecharDialogCancelar = function() {
                                $mdDialog.cancel();
                            };

                            $scope.cancelar = function (formularioCancelarLaudoRelatorio) {
                                $scope.validarFormulario(formularioCancelarLaudoRelatorio);
                                if (!formularioCancelarLaudoRelatorio.$valid) {
                                    return;
                                }
                                if(confirm($filter('translate')('mensagem-confirmar-cancelar-laudo-relrorio'))){

                                    $scope.pvgSpu.dataCancelamento = $scope.data;
                                    $scope.pvgSpu.justificativaCancelamento = $scope.justificativa;
                                    $scope.pvgSpu.motivoCancelamento = $scope.selecioneMotivo;
                                    $scope.pvgSpu.id = $scope.id;
                                    $scope.pvgSpu.ripImovel = vm.rip;
                                    $scope.pvgSpu.responsavelCancelamento = $scope.nomeResponsavel.responsavelCancelamento;

                                    pvgSpuService.cancelarAvaliacaoPvgSpu($scope.pvgSpu)
                                        .then(function() {
                                            $mdDialog.cancel();
                                            vm.buscarHistoricoPvgSpu();
                                            ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));

                                        });

                                }
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogCancelarLaudoAvaliacao.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });

                };

                vm.redirecionarConsultarAcoesAvaliacao = function() {
                    vm.filtroConsulta = {};
                    vm.filtroConsulta.ripImovel = vm.rip;
                    vm.filtroConsulta.tipoAcaoAvaliacaoList = ["AVALIACAO_PVG_SPU"];
                    vm.filtroConsulta.tipoStatusAcao = "PENDENTE";

                    avaliacaoEscopoCompartilhadoService.setObjetos('filtroConsulta', vm.filtroConsulta);
                    $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');
                };

                vm.desvincularPVGSpu = function() {

                    var rip = vm.rip;
                    $state.go('avaliacao.DESVINCULAR_AVALIACAO_PVG_SPU', {
                        rip: rip
                    });
                };

                vm.cadastrarPVGSpu = function() {

                    var rip = vm.rip;
                    $state.go('avaliacao.CADASTRAR_AVALIACAO_PVG_SPU', {
                        rip: rip
                    });
                };


                vm.detalharPVGSpu = function(idPvg) {

                    var rip = vm.rip;
                    $state.go('avaliacao.DETALHAR_AVALIACAO_PVG_SPU', {
                        rip: rip,
                        idPvg: idPvg
                    });

                };

                vm.editarFatores = function(idPvg) {

                    var rip = vm.rip;
                    $state.go('avaliacao.EDITAR_FATORES_AVALIACAO_PVG_SPU', {
                        rip: rip,
                        idPvg: idPvg
                    });

                };



                var pesquisarTrechosSelecionados = function () {
                    pvgSpuService.consultarTrechosSelecionados(vm.rip).then(function (resp) {
                        vm.trechos = resp.data;
                        vm.showDesvincularPvgSpu = vm.trechos.length > 1;
                    });
                };

                vm.calculaFator = function (fatores) {
                    var valor,fatorCorretivoTotal;
                    if(fatores && fatores.length > 0){
                        valor = fatores[0].valor;

                        for(var i = 1; i <fatores.length; i++){
                            valor *= fatores[i].valor;
                        }
                        if(valor > 2.5){
                            fatorCorretivoTotal = 2.5;
                        }else if(valor < 0.4){
                            fatorCorretivoTotal = 0.4;
                        }else{
                            fatorCorretivoTotal = valor;
                        }
                    }else{
                        return 1;
                    }
                    return fatorCorretivoTotal;
                };

                vm.excluirPvgSpu = function(idPvgSpu) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja excluir esta avaliação?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        pvgSpuService.excluirAvaliacaoPvgSpu(idPvgSpu).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('label-avaliacao-excluida-com-sucesso'));
                            vm.buscarHistoricoPvgSpu();
                        });
                    }, function () {

                    });
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["$mdDialog", "pvgAvaliacaoService", "$state", "$mdMedia", "usuarioAvaliacaoService", "comumAvaliacaoService", "ComumFactory", "formularioAvaliacaoService", "avaliacaoEscopoCompartilhadoService", "$filter"];
    angular.module('su-avaliacao')
        .directive('adicionarAvaliacaoPvgMunicipio', directive);

    function directive ($mdDialog, pvgAvaliacaoService, $state, $mdMedia, usuarioAvaliacaoService,
                        comumAvaliacaoService, ComumFactory, formularioAvaliacaoService, avaliacaoEscopoCompartilhadoService, $filter) {
        return {
            restrict: 'EA',
            scope: {
                rip: '=',
                uf: '=',
                areaTerreno: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarAvaliacaoPvgMunicipio.html',
            link: function ($scope) {

                var vm = $scope;

                var init = function() {
                    buscarUsuarioLogado();
                    vm.pvgsMunicipio = [];
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;

                    vm.pesquisarStatusPvg();
                    vm.buscarPvgsMunicipio();
                };

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                vm.buscarPvgsMunicipio = function(){
                    pvgAvaliacaoService.buscarPvgsMunicipio(vm.rip, (vm.registroInicial - 1), vm.tamanhoLimite).then(function (resp) {
                        vm.pvgsMunicipio = resp.data.content;
                        vm.totalItems = resp.data.totalElements;
                    });
                };

                vm.pesquisarStatusPvg = function() {
                    return comumAvaliacaoService.consultarStatusAvaliacao().then(function (resp) {
                        vm.listaStatusAvaliacao = resp.data;
                    });
                };

                vm.cancelarPvgMunicipio = function (idPvg) {
                    var rip = vm.rip;
                    $state.go('avaliacao.CANCELAR_AVALIACAO_PVG_MUNICIPIO', {
                        rip: rip,
                        idPvg: idPvg
                    });

                };


                vm.cadastrarPVGMunicipio = function() {

                    var rip = vm.rip;
                    $state.go('avaliacao.CADASTRAR_AVALIACAO_PVG_MUNICIPIO', {
                        rip: rip
                    });
                };


                vm.detalharPVGMunicipio = function(idPvg) {

                    var rip = vm.rip;
                    $state.go('avaliacao.DETALHAR_AVALIACAO_PVG_MUNICIPIO', {
                        rip: rip,
                        idPvg: idPvg
                    });

                };

                vm.redirecionarConsultarAcoesAvaliacao = function() {
                    vm.filtroConsulta = {};
                    vm.filtroConsulta.ripImovel = vm.rip;
                    vm.filtroConsulta.tipoAcaoAvaliacaoList = ["AVALIACAO_VALOR_VENAL"];
                    vm.filtroConsulta.tipoStatusAcao = "PENDENTE";

                    avaliacaoEscopoCompartilhadoService.setObjetos('filtroConsulta', vm.filtroConsulta);
                    $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');
                };

                vm.excluirPvgMunicipio = function(idPvg) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja excluir esta avaliação?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        pvgAvaliacaoService.excluirAvaliacaoPvg(idPvg).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('label-avaliacao-excluida-com-sucesso'));
                            vm.buscarPvgsMunicipio();
                        });
                    }, function () {

                    });
                };

                init();
            }
        };
    };

})();
/**
 * Created by tawan-souza on 16/03/17.
 */
(function () {

    directive.$inject = ["$mdDialog", "pvgAvaliacaoService", "formularioAvaliacaoService", "usuarioAvaliacaoService", "$mdMedia", "comumAvaliacaoService", "ComumFactory", "$filter", "avaliacaoEscopoCompartilhadoService", "$state"];
    angular.module('su-avaliacao')
        .directive('adicionarAvaliacaoPvgIncra', directive);

    function directive ($mdDialog, pvgAvaliacaoService, formularioAvaliacaoService, usuarioAvaliacaoService,
                        $mdMedia, comumAvaliacaoService, ComumFactory, $filter, avaliacaoEscopoCompartilhadoService, $state) {
        return {
            restrict: 'EA',
            scope: {
                rip: '=',
                uf: '=',
                areaTerreno: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarAvaliacaoPvgIncra.html',
            link: function ($scope) {

                var vm = $scope;

                var init = function() {
                    buscarUsuarioLogado();
                    vm.pvgsIncra = [];
                    vm.registroInicial = 1;
                    vm.tamanhoLimite = 5;
                    vm.totalItems = 0;
                    vm.pesquisarStatusPvg();
                    vm.buscarPvgsIncra();
                };

                var buscarUsuarioLogado = function (callback) {
                    usuarioAvaliacaoService.getUsuarioLogado().then(function (resp) {
                        vm.usuarioLogado = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                vm.buscarPvgsIncra = function(){
                    pvgAvaliacaoService.buscarPvgsIncra(vm.rip, (vm.registroInicial - 1), vm.tamanhoLimite)
                            .then(function (resp) {
                        vm.pvgsIncra = resp.data.content;
                        vm.totalItems = resp.data.totalElements;
                    });
                };

                vm.pesquisarStatusPvg = function() {
                    return comumAvaliacaoService.consultarStatusAvaliacao().then(function (resp) {
                        vm.listaStatusAvaliacao = resp.data;
                    });
                };

                vm.cancelarPvgIncra = function (idPvg) {
                    var rip = vm.rip;
                    $state.go('avaliacao.CANCELAR_AVALIACAO_PVG_INCRA', {
                        rip: rip,
                        idPvg: idPvg
                    });
                };


                vm.abrirDialogAvaliacaoIncra = function(ev, idPvg, detalharPvgIncra) {
                    var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));

                    var rip = vm.rip;

                    $mdDialog.show({
                        controller: ["$scope", function ($scope) {
                            $scope.detalharPvgIncra = detalharPvgIncra;
                            $scope.rip = rip;

                            var formularioServiceInstance = formularioAvaliacaoService.getInstance();
                            $scope.validarFormulario = formularioServiceInstance.validarFormulario;

                            $scope.pvgIncra = {
                                documentos: [],
                                valorTerreno: 0
                            };

                            var INCRA = 'INCRA';
                            $scope.M2 = 'M2';
                            $scope.HA = 'HA';

                            $scope.formatterTipoUnidade = '';
                            $scope.listaTipoUnidades = [];

                            var init = function() {
                                if (idPvg) {
                                    pvgAvaliacaoService.consultarPorId(idPvg).then(function(resp) {
                                        $scope.pvgIncra = resp.data;

                                        $scope.pvgIncra.dataInicial = comumAvaliacaoService.recuperarData($scope.pvgIncra.dataInicial);
                                        $scope.pvgIncra.dataFinal = $scope.pvgIncra.dataFinal ?
                                            comumAvaliacaoService.recuperarData($scope.pvgIncra.dataFinal) : undefined;
                                        $scope.areaTerreno = $scope.pvgIncra.areaTerreno;
                                    });
                                }

                                comumAvaliacaoService.buscarTiposUnidade().then(function(resp) {
                                    $scope.listaTipoUnidades = resp.data;
                                });
                            };

                            $scope.formatarTipoUnidadeValor = function() {
                                if ($scope.pvgIncra.tipoUnidade == $scope.M2) {
                                    $scope.formatterTipoUnidade = '/m²';
                                } else if ($scope.pvgIncra.tipoUnidade == $scope.HA) {
                                    $scope.formatterTipoUnidade = '/ha';
                                }
                                limparValoresTerreno();
                            };

                            var limparValoresTerreno = function() {
                                $scope.pvgIncra.valorTerreno = undefined;
                                $scope.pvgIncra.valor = undefined;
                            };

                            $scope.calcularValorTerreno = function() {
                                var valorCalculado = $scope.pvgIncra.valor * $scope.areaTerreno;
                                $scope.pvgIncra.valorTerreno = valorCalculado.toFixed(2)
                            };

                            var validarDataMaior = function() {
                                if ($scope.pvgIncra.dataInicial > $scope.pvgIncra.dataFinal) {
                                    ComumFactory.mostrarMensagemError('Período inválido.');
                                    throw 'Período inválido.';
                                }
                            };

                            var validarDatasPorDataAtual = function() {
                                if ($scope.pvgIncra.dataInicial > new Date()
                                    || $scope.pvgIncra.dataFinal > new Date()) {
                                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-nao-pode-posterior-data-atual'));
                                    throw $filter('translate')('mensagem-data-nao-pode-posterior-data-atual');
                                }
                            };


                            $scope.salvarDocumentoIncra = function (formularioPvgIncra) {
                                $scope.validarFormulario(formularioPvgIncra);

                                if (!formularioPvgIncra.$valid) {
                                    return;
                                }

                                validarDataMaior();
                                validarDatasPorDataAtual();
                                $scope.pvgIncra.ripImovel = vm.rip;
                                $scope.pvgIncra.tipo = INCRA;
                                $scope.pvgIncra.areaTerreno = $scope.areaTerreno;

                                pvgAvaliacaoService.salvarPvg($scope.pvgIncra).then(function() {
                                    $mdDialog.cancel();
                                    vm.buscarPvgsIncra();

                                    ComumFactory.mostrarMensagemSucesso($filter('translate')('mensagem-sucesso-dados-gravados'));
                                });
                            };

                            init();
                        }],
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogAvaliacaoPvgIncra.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: useFullScreen
                    });
                };

                vm.cadastrarPVGIncra = function() {

                    var rip = vm.rip;
                    $state.go('avaliacao.CADASTRAR_AVALIACAO_PVG_INCRA', {
                        rip: rip
                    });
                };


                vm.detalharPVGIncra = function(idPvg) {

                    var rip = vm.rip;
                    $state.go('avaliacao.DETALHAR_AVALIACAO_PVG_INCRA', {
                        rip: rip,
                        idPvg: idPvg
                    });

                };

                vm.redirecionarConsultarAcoesAvaliacao = function() {
                    vm.filtroConsulta = {};
                    vm.filtroConsulta.ripImovel = vm.rip;
                    vm.filtroConsulta.tipoAcaoAvaliacaoList = ["AVALIACAO_INCRA"];
                    vm.filtroConsulta.tipoStatusAcao = "PENDENTE";

                    avaliacaoEscopoCompartilhadoService.setObjetos('filtroConsulta', vm.filtroConsulta);
                    $state.go('avaliacao.CONSULTAR_ACOES_AVALIACAO');
                };

                vm.excluirPvgIncra = function(idPvg) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja excluir esta avaliação?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        pvgAvaliacaoService.excluirAvaliacaoPvg(idPvg).then(function () {
                            ComumFactory.mostrarMensagemSucesso($filter('translate')('label-avaliacao-excluida-com-sucesso'));
                            vm.buscarPvgsIncra();
                        });
                    }, function () {

                    });
                };

                init();
            }
        };
    };

})();
(function () {

    angular.module('su-avaliacao')
        .directive('cadastroPvgs', directive);

    function directive () {
        return {
            restrict: 'EA',
            scope: {
                rip: '=',
                imovel: '=',
                areaTerreno: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-avaliacao/templates/inserirCadastroPvgs.html',
            link: function ($scope) {

                var init = function() {
                    $scope.terreno = {};

                    $scope.dadosAvaliacao={
                        unidadeImoveis:[],
                        isUnidadeAutonoma:false,
                        valorImovelCalculado:false
                    };

                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["dominioAvaliacaoService"];
    angular.module('su-avaliacao')
        .directive('cadastroLaudoUnidadeAutonoma', directive);

    function directive (dominioAvaliacaoService) {
        return {
            restrict: 'EA',
            scope: {
                rip: '=',
                uf: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-avaliacao/templates/inserirLaudoDadosUnidadeAutonoma.html',
            link: function ($scope) {

                

                var init = function() {
                    $scope.ambienteDev = dominioAvaliacaoService.ehAmbienteDesenvolvimento();
                };



                init();
            }
        };
    };

})();


(function () {

    directive.$inject = ["arquivoAvaliacaoService", "$filter", "ComumFactory"];
    angular.module('su-avaliacao')
        .directive('cadastrarDocumentosLaudoAvaliacao', directive);

    function directive (arquivoAvaliacaoService, $filter, ComumFactory) {
        return {
            restrict: 'EA',
            scope: {
                laudoAvaliacao: '=',
                detalharLaudo: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-avaliacao/templates/inserirDocumentosLaudoAvaliacao.html',
            link: function ($scope) {
                var vm = $scope;

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                vm.paginationDocumentos = [];

                var MEDIA_TYPE_DOC = 'application/msword';
                var MEDIA_TYPE_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                var MEDIA_TYPE_PDF = 'application/pdf';
                var MEDIA_TYPE_XLS = 'application/vnd.ms-excel';
                var MEDIA_TYPE_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

                var init = function() {
                    limparCamposDocumentoLaudo();
                };

                vm.adicionarArquivoDocumentoLaudo = function(file) {

                    if(file[0].type == MEDIA_TYPE_DOC || file[0].type == MEDIA_TYPE_DOCX || file[0].type == MEDIA_TYPE_PDF
                        || file[0].type == MEDIA_TYPE_XLS || file[0].type == MEDIA_TYPE_XLSX) {
                        arquivoAvaliacaoService.uploadArquivo(file[0]).then(function(resp) {
                            vm.arquivoTemp = resp.data;
                        });
                    }else{
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-formato-invalido'));
                    }
                };

                var limparCamposDocumentoLaudo = function() {
                    vm.documentoLaudoRelatorio = null;
                    vm.arquivoTemp = null;
                };

                vm.adicionarListaDocumentoLaudoRelatorio = function() {
                    if (!vm.documentoLaudoRelatorio || !vm.arquivoTemp) {
                        ComumFactory.mostrarMensagemError('Informe um nome e arquivo para o documento.');
                    } else {
                        if (!vm.laudoAvaliacao.arquivosLaudo) {
                            vm.laudoAvaliacao.arquivosLaudo = [];
                        }

                        vm.laudoAvaliacao.arquivosLaudo.push({
                            nomeArquivo: vm.documentoLaudoRelatorio,
                            arquivo: vm.arquivoTemp
                        });
                        limparCamposDocumentoLaudo();
                        vm.paginacaoDocumentosLaudo();
                    }
                };

                vm.downloadDocumento = function(idArquivo){
                    arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                $scope.removerDocumentoLaudoRelatorio = function($event, index, idArquivo) {
                    if (!vm.detalharLaudoRelatorio) {
                        if (confirm('Deseja remover este arquivo?')) {
                            arquivoAvaliacaoService.deletarArquivo(idArquivo).then(function() {
                                vm.laudoAvaliacao.arquivosLaudo.splice(index, 1);
                                vm.paginacaoDocumentosLaudo();
                            });
                        }
                    }
                };

                vm.paginacaoDocumentosLaudo = function() {
                    vm.paginationDocumentos = [];
                        if (vm.laudoAvaliacao.arquivosLaudo) {
                        vm.totalItems = vm.laudoAvaliacao.arquivosLaudo.length;
                        if (vm.totalItems < vm.tamanhoLimite) {
                            vm.paginationDocumentos = vm.laudoAvaliacao.arquivosLaudo;
                        } else {
                            for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                                vm.paginationDocumentos.push(vm.laudoAvaliacao.arquivosLaudo[i]);
                                if (vm.paginationDocumentos.length == vm.tamanhoLimite) {
                                    break;
                                }
                            }
                        }
                    }
                };

                init();
            }
        };
    };

})();

(function () {
    'use strict';

    directive.$inject = ["ciMapaAvaliacaoLayersFactory", "ciMapaAvaliacaoViewFactory", "NotificadorService", "MAPA_CONSTANTS", "imovelAvaliacaoService", "$timeout", "$filter", "localStorageService"];
    angular.module('su-avaliacao')
        .directive('areaLateralMapaSlick', directive);

    function directive(ciMapaAvaliacaoLayersFactory, ciMapaAvaliacaoViewFactory, NotificadorService, MAPA_CONSTANTS, imovelAvaliacaoService, $timeout, $filter, localStorageService) {
        return {
            restrict: 'EA',
            scope: {
                rip: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/secoes-avaliacao/templates/areaLateralMapaESlickCarossel.html',
            link: function ($scope) {

                var vm = $scope;
                vm.carregarMapa = false;
                ciMapaAvaliacaoLayersFactory.init();
                ciMapaAvaliacaoViewFactory.init();
                vm.$watch('rip', function () {
                    if (vm.rip) {
                        imovelAvaliacaoService.consultarImovelPorRip(vm.rip).then(function (resposta) {
                            vm.imovel = resposta.data;

                            if (vm.imovel) {
                                imovelAvaliacaoService.buscarBenfeitoriasSuPorRip(vm.rip).then(
                                    function (resp) {
                                        vm.imovel.benfeitorias = resp.data;
                                        vm.carregarMapa = true;
                                        carregarMapaComPonto(vm.imovel);
                                    },
                                    function () {
                                        vm.carregarMapa = true;
                                        carregarMapaComPonto(vm.imovel);
                                    }
                                );
                            }

                            if (vm.imovel && vm.imovel.enderecamento) {
                                vm.enderecoImovel = criarEnderecoImovel(vm.imovel.enderecamento);
                            } else {
                                vm.enderecoImovel = 'Endereço não disponível';
                            }
                        });
                    }
                });

                function carregarMapaComPonto(imovel) {
                    loadMapas(function () {
                        $timeout(function () {
                            marcarPontoMapa(imovel);
                        }, 1000);
                    });
                }

                function marcarPontoMapa(imovel) {
                    if (!imovel) return;

                    localStorageService.set('pontoImovel', imovel);

                    if (imovel.pontoImovel && imovel.pontoImovel.coordinates) {
                        NotificadorService.notify(vm, imovel.pontoImovel.coordinates, MAPA_CONSTANTS.NOTIFICADORES.ATUALIZAR_COORDENADAS_DO_PONTO);
                    }

                    NotificadorService.notify(vm, imovel, MAPA_CONSTANTS.NOTIFICADORES.MARCAR_PONTO_POR_COORDENADAS);
                }

                function criarEnderecoImovel(enderecamento) {
                    var logradouro = ifVazio(enderecamento.logradouro, 'Desconhecido'),
                        tipoLogradouro = ifVazio(enderecamento.tipoLogradouro, ''),
                        numero = ifVazio(enderecamento.numero, 'S/N'),
                        bairro = ifVazio(enderecamento.bairro, ''),
                        municipio = ifVazio(enderecamento.municipio, 'Cidade não informada'),
                        uf = ifVazio(enderecamento.uf, ''),
                        pais = ifVazio(enderecamento.pais, 'País não informado'),
                        cep = ifVazio(enderecamento.cep, 'CEP não disponível');

                    function ifVazio(n, valorPadrao) {
                        return (typeof n === 'string' && n.trim()) ? n : valorPadrao;
                    }

                    if (pais !== 'Brasil') {
                        return municipio + " - " + pais + (cep !== 'CEP não disponível' ? ". CEP: " + cep : '');
                    }

                    return tipoLogradouro + " " + logradouro + ", " + numero + ", " + bairro + ", " + municipio + " / " + uf + " - " + pais + ". CEP: " + cep;
                }

                function loadMapas(callback) {

                    ciMapaAvaliacaoLayersFactory.init();
                    ciMapaAvaliacaoViewFactory.init();
                    if (callback) {
                        $timeout(callback);
                    }
                };
            }
        };
    };

})();
(function () {

    directive.$inject = ["ComumFactory", "$filter", "laudoRelatorioService", "imovelAvaliacaoService"];
    angular.module('su-avaliacao')
        .directive('vistoriaLaudo', directive);

    function directive(ComumFactory, $filter, laudoRelatorioService, imovelAvaliacaoService) {
        return {
            restrict: 'EA',
            scope: {
                identificacao: '=',
                vistoria: '=',
                listaFotosUnidadeAutonoma: '=',
                listaFotosBenfeitorias: '=',
                listaFotosImovel: '=',
                formularioValidacao: '=',
                tipoAvaliacao: '=',
                listaBenfeitoria: '=',
                unidadeAutonoma: '=',
                detalharLaudoRelatorio: '=',
                vistoriaListaFotosImovel: '=',
                vistoriaListaFotosBenfeitorias: '=',
                vistoriaListaFotosUnidadeAutonoma: '=',
                anexoLaudoRelatorio: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/vistoria.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.LAUDO = 'LAUDO';
                vm.RELATORIO = 'RELATORIO';
                vm.listaExecucaoVistoria = [];
                vm.dataAtual = new Date();

                var init = function () {

                    vm.vistoria.isNaoPossuiVistoria = !vm.vistoria.execucaoVsitoria || vm.vistoria.execucaoVsitoria === 'VISTORIA_NAO_REALIZADA';
                    buscarExecucoesVistoria();
                };

                var buscarExecucoesVistoria = function () {
                    laudoRelatorioService.buscarExecucoesVistoria().then(function (resp) {
                        vm.listaExecucaoVistoria = resp.data;
                    });
                };

                vm.validarDataVistoria = function () {
                    if (vm.vistoria.dataVistoria > new Date()) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-data-invalida'));
                        vm.vistoria.dataVistoria = '';
                    }
                };


                vm.selecionarVistoriaImovel = function () {
                    if(vm.listaFotosUnidadeAutonoma) {
                        vm.listaFotosUnidadeAutonoma.length = 0;
                    }
                    if(vm.listaFotosBenfeitorias) {
                        vm.listaFotosBenfeitorias.length = 0;
                    }
                    if(vm.listaFotosImovel) {
                        vm.listaFotosImovel.length = 0;
                    }

                    if(vm.vistoriaListaFotosImovel) {
                        vm.vistoriaListaFotosImovel.length = 0;
                    }
                    if(vm.vistoriaListaFotosBenfeitorias) {
                        vm.vistoriaListaFotosBenfeitorias.length = 0;
                    }
                    if(vm.vistoriaListaFotosUnidadeAutonoma) {
                        vm.vistoriaListaFotosUnidadeAutonoma.length = 0;
                    }

                    for (i = 0; i < vm.anexoLaudoRelatorio.anexos.length; i++) {
                        if (vm.anexoLaudoRelatorio.anexos[i].ordem === 0) {
                            vm.anexoLaudoRelatorio.anexos[i].filhos = [];
                            break;
                        }
                    }

                    if (vm.vistoria.execucaoVistoria === 'VISTORIA_NAO_REALIZADA') {
                        vm.vistoria.dataVistoria = '';
                        vm.vistoria.isNaoPossuiVistoria = true;

                        var idsBenfeitorias = [];
                        buscarImagensImovelPorRip(vm.identificacao.rip)
                        if (vm.listaBenfeitoria != null && vm.listaBenfeitoria.length > 0) {
                            angular.forEach(vm.listaBenfeitoria, function (benfeitoria) {
                                idsBenfeitorias.push(benfeitoria.id);
                            });
                            buscarImagensBenfeitorias(idsBenfeitorias);
                        } else if(vm.unidadeAutonoma) {
                            buscarImagensUnidadeAutonoma(vm.unidadeAutonoma)
                        }

                    } else {
                        vm.vistoria.isNaoPossuiVistoria = false;
                        vm.vistoria.justificativa = null;
                        if (vm.vistoria.execucaoVistoria === 'VISTORIA_REALIZADA_EM_UM_DIA') {
                            vm.vistoria.dataVistoria = '';
                        }
                    }
                };

                var buscarImagensImovelPorRip = function (ripImovel) {
                    imovelAvaliacaoService.buscarFotosImovel(ripImovel).then(function (resp) {
                        vm.listaFotosImovel = resp.data;
                    });
                };

                var buscarImagensBenfeitorias = function (idsBenfeitorias) {
                    if (idsBenfeitorias && idsBenfeitorias.length > 0) {
                        benfeitoriasLaudoRelatorioService.buscarFotosBenfeitorias(idsBenfeitorias.toString())
                            .then(function (imagens) {
                                vm.listaFotosBenfeitorias = imagens.data;
                            });
                    }
                };

                var buscarImagensUnidadeAutonoma = function (unidadeAutonoma) {
                    if (unidadeAutonoma) {
                        unidadeAutonomaAvaliacaoService.buscarImagensUnidadeAutonoma(unidadeAutonoma.idOriginal)
                            .then(function (resp) {
                                vm.listaFotosUnidadeAutonoma = resp.data;
                            });
                    }
                };

                init();
            }
        };
    }

})();
(function () {

    directive.$inject = ["unidadeAutonomaAvaliacaoService"];
    angular.module('su-avaliacao').directive('unidadeAutonomaLaudo', directive);

    function directive(unidadeAutonomaAvaliacaoService) {
        return {
            restrict: 'EA',
            scope: {
                unidadeAutonoma: '=',
                unidadeAutonomaIncorporacao: '=',
                redirecionarCadastrarDocumentoEdificacaoUnidadeAutonoma: '=',
                imovel: '=',
                formularioValidacao: '=',
                detalharLaudoRelatorio: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/unidadeAutonoma.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaTiposEdificacao = [];
                vm.listaTiposProjeto = [];

                vm.isTipoEdificacao = true;
                vm.isTipoProjeto = true;
                vm.isPossuiElevador = true;
                vm.isNomeEdificacao = true;
                vm.isObservacoesGeraisEdificacao = true;
                vm.isVagas = true;
                vm.isMobilidade = true;
                vm.isBanheiro = true;
                vm.isElevador = true;
                vm.isAreaConstruida = true;
                vm.isNumeroDormitorios = true;
                vm.isMemorialDescritivo = true;
                vm.isObservacoesGeraisUnidadeAutonoma = true;
                vm.isVagasGaragemCobertas = true;
                vm.isVagasGaragemDescobertas = true;

                vm.SIM = 'SIM';
                vm.NAO = 'NAO';
                vm.SEMINFO = 'SEM INFO';
                vm.condicoes = [
                    {
                        nome: "Sim",
                        valor: true

                    }, {
                        nome: "Não",
                        valor: false
                    }
                ];

                var init = function () {
                    buscarTiposEdificacao();
                    buscarTiposProjeto();
                };

                var buscarTiposEdificacao = function () {
                    unidadeAutonomaAvaliacaoService.buscarTiposEdificacao().then(function (resp) {
                        vm.listaTiposEdificacao = resp.data;
                    });
                };

                var buscarTiposProjeto = function () {
                    unidadeAutonomaAvaliacaoService.buscarTiposProjeto().then(function (resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                init();
            }
        };
    }

})();
(function () {

    angular.module('su-avaliacao')
        .directive('terrenoImovelLaudo', directive);

    function directive() {
        return {
            restrict: 'EA',
            scope: {
                imovel: '=',
                terrenoImovel: '=',
                documentosParametrosUrbanisticos: '=',
                terreno: '=',
                formularioValidacao: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/terrenoImovel.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.METRO_QUADRADO = 'METRO_QUADRADO';
                vm.HECTARE = 'HECTARE';

                vm.isMemorialDescritivo = true;
                vm.isUnidadeMedidaArea = true;
                vm.isAreaTotal = true;
                vm.isAreaUniao = true;
                vm.isFracaoIdealImovel = true;
                vm.isFracaoIdealUniao = true;
                vm.isMemorialDescritivoAreaUniao = true;

                vm.CONTENT_TYPE_01 = 'CONTENT_TYPE_01';

                vm.$watch('imovel.terrenoImovel', function (terrenoImovel) {
                    if (terrenoImovel) {
                        vm.terreno = {
                            memorialDescritivo: (terrenoImovel.geometriaTerrenoCartorial ? terrenoImovel.memorialDescritivoCartorial : terrenoImovel.memorialDescritivoCadastral),
                            unidadeMedida: (terrenoImovel.geometriaTerrenoCartorial ? terrenoImovel.unidadeMedidaCartorial : terrenoImovel.unidadeMedidaCadastral),
                            areaTotal: (terrenoImovel.geometriaTerrenoCartorial ? terrenoImovel.areaTotalCartorial : terrenoImovel.areaTotalCadastral),
                            areaUnial: (terrenoImovel.geometriaTerrenoCartorial ? terrenoImovel.areaUniaoCartorial : terrenoImovel.areaUniaoCadastral),
                            fracaoIdealImovel: (terrenoImovel.geometriaTerrenoCartorial ? terrenoImovel.fracaoIdealImovelCartorial : terrenoImovel.fracaoIdealImovelCadastral),
                            fracaoIdealUniao: (terrenoImovel.geometriaTerrenoCartorial ? terrenoImovel.fracaoIdealUniaoCartorial : terrenoImovel.fracaoIdealUniaoCadastral),
                            memorialDescritivoAreaUniao: (terrenoImovel.geometriaTerrenoCartorial ? terrenoImovel.memorialDescritivoCartorialUniao : terrenoImovel.memorialDescritivoCadastralUniao)
                        }
                    }
                });
            }
        };
    }

})();
(function () {

    directive.$inject = ["avaliacaoLaudoService", "numeroExtensoAvaliacaoService", "identificacaoLaudoRelatorioService", "ComumFactory", "$filter"];
    angular.module('su-avaliacao')
        .directive('avaliacaoLaudoResultadosRelatorio', directive);

    function directive(avaliacaoLaudoService, numeroExtensoAvaliacaoService, identificacaoLaudoRelatorioService, ComumFactory, $filter) {
        return {
            restrict: 'EA',
            scope: {
                avaliacao: '=',
                identificacao: '=',
                laudoRelatorioAvaliacao: '=',
                formularioValidacao: '=',
                ferramentaAnalitica: '=',
                listaBenfeitoriaAvaliacao: '=',
                unidadeAutonomaIncorporacao: '=',
                redirecionarInformarValorCub: '=',
                redirecionarInformarValorEdificacao: '=',
                tipoValorLaudo: '=',
                detalharLaudoRelatorio: '=',
                unidadeMedida: '=',
                areaTotalTerreno: '=',
                anexoLaudoRelatorio: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/resultadosAvaliacao.tmpl.html',
            link: function ($scope) {

                var vm = $scope;
                vm.listaTiposValores = [];
                vm.listaNivelFundamentacao = [];
                vm.ferramentasAnaliticas = [];
                vm.VALOR_MEDIO = 'VALOR_MEDIO';
                vm.VALOR_MINIMO = 'VALOR_MINIMO';
                vm.VALOR_MAXIMO = 'VALOR_MAXIMO';
                vm.OUTRO_VALOR = 'OUTRO_VALOR';

                vm.NAO_ATINGIDO = 'NAO_ATINGIDO';

                vm.CONTENT_TYPE_02 = 'CONTENT_TYPE_02';

                vm.LAUDO_RELATORIO = 'LAUDO_RELATORIO';
                vm.formatterValorMedio = null;
                vm.formatterValorMinimo = null;
                vm.formatterValorMaximo = null;
                vm.formatterOutroValorAdotado = null;
                vm.formatterValorAdotado = null;

                vm.variacaoValorAdotado = null;
                vm.variacaoSuperiorValorMedio = null;
                vm.variacaoInferiorValorMedio = null;
                vm.valorAdotadoSelecionado = 0;

                vm.enquadramentoGrau3 = false;
                vm.enquadramentoGrau2 = false;
                vm.enquadramentoGrau1 = false;
                vm.enquadramentoGrauNaoAtingido = false;

                var TRES = 'TRES';
                var DOIS = 'DOIS';
                var UM = 'UM';

                vm.icAdocaoEstimativa = true;
                vm.mostrarJustificativaValor = false;

                var init = function () {
                    buscarMoedas();
                    if(!vm.areaTotalTerreno) {
                        vm.areaTotalTerreno = vm.laudoRelatorioAvaliacao.terrenoImovel.terreno.areaTotal;
                    }
                };

                vm.$watch('avaliacao', function () {
                    if(vm.avaliacao && vm.avaliacao.resultadosAvaliacao) {
                        vm.avaliacao.resultadosAvaliacao.valorCalculado += vm.avaliacao.valorTerreno;
                        vm.avaliacao.resultadosAvaliacao.valorCalculado += vm.avaliacao.valorTotalTerreno;

                        vm.calcularAmplitude();
                        buscarTiposValores();
                        buscarFerramentasAnaliticas();
                        if (!vm.avaliacao.anexos) {
                            vm.avaliacao.anexos = [];
                        }

                        if(vm.laudoRelatorioAvaliacao.identificacao.metodologia === 'OUTRO'){
                            vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorAdotado;
                        }

                        buscarMoedas(function () {
                            vm.calcularVariacaoValorAdotado();
                            for (var i = 0; i < vm.listaMoedas.length; i++) {
                                if (vm.avaliacao.resultadosAvaliacao.moeda.id === vm.listaMoedas[i].id) {
                                    vm.avaliacao.resultadosAvaliacao.moeda = vm.listaMoedas[i];
                                }
                            }
                            vm.calcularValorAdotado();
                            vm.validarValorImovel();
                        });


                    }
                });

                var buscarMoedas = function (callback) {
                    avaliacaoLaudoService.getMoedas().then(function (resp) {
                        vm.listaMoedas = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                var buscarTiposValores = function () {
                    avaliacaoLaudoService.ordenacaoTipoValor().then(function (resp) {
                        vm.listaTiposValores = resp.data;
                    });
                };

                var buscarFerramentasAnaliticas = function () {
                    identificacaoLaudoRelatorioService.buscarFerramentasAnaliticas().then(function (resp) {
                        vm.ferramentasAnaliticas = resp.data;
                    });
                };

                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService
                        .converterNumeroPorExtenso(numero, vm.avaliacao.resultadosAvaliacao.moeda.nome.toLowerCase());
                };

                vm.alterandoMoedaValores = function () {
                    if (vm.avaliacao.resultadosAvaliacao) {
                        vm.formatterValorMedio = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMedio);
                        vm.formatterValorMinimo = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMinimo);
                        vm.formatterValorMaximo = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMaximo);
                        vm.formatterOutroValorAdotado = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.outroValorAdotado);
                        vm.calcularVariacaoValorAdotado();
                    }
                };

                vm.calcularVariacaoValorAdotado = function () {
                    vm.determinarValorAdotadoSelecionado();
                    if (vm.avaliacao.resultadosAvaliacao.valorMedio) {
                        vm.variacaoSuperiorValorMedio = (vm.avaliacao.resultadosAvaliacao.valorMedio + (vm.avaliacao.resultadosAvaliacao.valorMedio * 0.01));
                        vm.variacaoInferiorValorMedio = (vm.avaliacao.resultadosAvaliacao.valorMedio - (vm.avaliacao.resultadosAvaliacao.valorMedio * 0.01));
                        vm.verificarAdocaoEstimativaValor();
                    }
                };

                vm.verificarAdocaoEstimativaValor = function () {
                    vm.icAdocaoEstimativa = true;
                    if((vm.avaliacao.resultadosAvaliacao.tipoValor !== vm.VALOR_MEDIO) && vm.valorAdotadoSelecionado && ( vm.valorAdotadoSelecionado < vm.variacaoInferiorValorMedio || vm.valorAdotadoSelecionado > vm.variacaoSuperiorValorMedio)) {
                        vm.icAdocaoEstimativa = false;
                    }
                };

                vm.determinarValorAdotadoSelecionado = function () {
                    if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MEDIO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMedio;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MINIMO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMinimo;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MAXIMO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMaximo;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.OUTRO_VALOR) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.outroValorAdotado;
                    }
                };

                vm.calcularAmplitude = function () {
                    var valor1 = 0;
                    var valor2 = 0;
                    if (vm.avaliacao.resultadosAvaliacao) {
                        if (vm.avaliacao.resultadosAvaliacao.valorMedio && vm.avaliacao.resultadosAvaliacao.valorMaximo && vm.avaliacao.resultadosAvaliacao.valorMinimo) {
                            valor1 = ((vm.avaliacao.resultadosAvaliacao.valorMedio - vm.avaliacao.resultadosAvaliacao.valorMinimo) * 100) / vm.avaliacao.resultadosAvaliacao.valorMedio;
                            valor2 = ((vm.avaliacao.resultadosAvaliacao.valorMaximo - vm.avaliacao.resultadosAvaliacao.valorMedio) * 100) / vm.avaliacao.resultadosAvaliacao.valorMaximo;
                            vm.avaliacao.enquadramento.amplitudeIntervalo = valor1 + valor2;
                        }
                    }
                };

                vm.validarValores = function () {

                    if (vm.avaliacao.resultadosAvaliacao.valorMinimo && vm.avaliacao.resultadosAvaliacao.valorMedio) {
                        if (vm.avaliacao.resultadosAvaliacao.valorMinimo > vm.avaliacao.resultadosAvaliacao.valorMedio) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-mininimo-maior-medio'));
                            vm.avaliacao.resultadosAvaliacao.valorMinimo = null;
                            return;
                        }
                    }
                    if (vm.avaliacao.resultadosAvaliacao.valorMedio && vm.avaliacao.resultadosAvaliacao.valorMaximo) {
                        if (vm.avaliacao.resultadosAvaliacao.valorMedio > vm.avaliacao.resultadosAvaliacao.valorMaximo) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-médio-maior-maximo'));
                            vm.avaliacao.resultadosAvaliacao.valorMaximo = null;
                            return;
                        }
                    }

                    if(vm.laudoRelatorioAvaliacao.identificacao.metodologia === 'OUTRO'){
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorAdotado;
                    }
                };

                vm.calcularValorAdotado = function () {

                    var varSuperior = vm.variacaoSuperiorValorCalculado;
                    var varInferior = vm.variacaoInferiorValorCalculado;
                    var valorCalculado = vm.avaliacao.resultadosAvaliacao.valorCalculado;

                    if (valorCalculado){
                        varSuperior = (valorCalculado + (valorCalculado * 0.01));
                        varInferior = (valorCalculado - (valorCalculado * 0.01));
                    }

                    if((varInferior > valorCalculado) && (valorCalculado < varSuperior)){
                        vm.mostrarJustificativaValor = true;
                    }else{
                        vm.mostrarJustificativaValor = false;
                        vm.avaliacao.resultadosAvaliacao.justificativaValorAdotad = null;
                    }
                };

                vm.calcularValorAdotadoInferencia = function () {
                    vm.calcularVariacaoValorAdotado();

                    var varSuperior = vm.variacaoSuperiorValorCalculado;
                    var varInferior = vm.variacaoInferiorValorCalculado;
                    var valorMedio = vm.avaliacao.resultadosAvaliacao.valorMedio;
                    var valorAdotado = vm.avaliacao.resultadosAvaliacao.tipoValor;
                    if(vm.avaliacao.resultadosAvaliacao.tipoValor){
                        switch (vm.avaliacao.resultadosAvaliacao.tipoValor) {
                            case('VALOR_MEDIO'):
                                valorAdotado = vm.avaliacao.resultadosAvaliacao.valorMedio;
                                break;
                            case('VALOR_MINIMO'):
                                valorAdotado = vm.avaliacao.resultadosAvaliacao.valorMinimo ;
                                break;
                            case('VALOR_MAXIMO'):
                                valorAdotado = vm.avaliacao.resultadosAvaliacao.valorMaximo ;
                                break;
                            case('OUTRO_VALOR'):
                                valorAdotado = vm.avaliacao.resultadosAvaliacao.outroValorAdotado;
                                break;
                        }
                    }

                    if (valorAdotado){
                        varSuperior = (valorMedio + (valorMedio * 0.01));
                        varInferior = (valorMedio - (valorMedio * 0.01));
                    }

                    if((varInferior > valorAdotado) || (varSuperior < valorAdotado)){
                        vm.mostrarJustificativaValor = true;
                    }else{
                        vm.mostrarJustificativaValor = false;
                        vm.avaliacao.resultadosAvaliacao.justificativaValorAdotad = null;
                    }
                };

                vm.validarValorImovel = function () {
                    if (vm.avaliacao.tipoValorLaudo === 'INFORMAR_VALORES_LAUDO') {
                        if (!vm.valorAdotadoSelecionado) {
                            vm.valorAdotadoSelecionado = 0;
                        }
                        if (vm.avaliacao.valorTerreno > vm.valorAdotadoSelecionado || vm.avaliacao.valorTerreno < 0) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                            vm.avaliacao.valorTerreno = null;
                            for (i = 0; i < vm.listaBenfeitoriaAvaliacao.length; i++) {
                                vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.valor = 0;
                            }
                            return;
                        }
                    }else if(vm.avaliacao.tipoValorLaudo === 'ESTIMAR_VALORES'){
                        var total = 0;
                        if (!vm.valorAdotadoSelecionado) {
                            vm.valorAdotadoSelecionado = 0;
                        }

                        for (i = 0; i < vm.listaBenfeitoriaAvaliacao.length; i++) {
                            total += vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.valor;
                        }

                        if(total > vm.valorAdotadoSelecionado){
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                        }
                    }
                };

                vm.limparDadosArquivosModelo = function () {
                    vm.avaliacao.resultadosAvaliacao.arquivosModelo = [];
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["avaliacaoLaudoService", "numeroExtensoAvaliacaoService", "identificacaoLaudoRelatorioService", "ComumFactory", "$filter", "$timeout"];
    angular.module('su-avaliacao')
        .directive('avaliacaoLaudoResultados', directive);

    function directive(avaliacaoLaudoService, numeroExtensoAvaliacaoService, identificacaoLaudoRelatorioService, ComumFactory, $filter, $timeout) {
        return {
            restrict: 'EA',
            scope: {
                avaliacao: '=',
                identificacao: '=',
                laudoRelatorioAvaliacao: '=',
                formularioValidacao: '=',
                ferramentaAnalitica: '=',
                listaBenfeitoriaAvaliacao: '=',
                unidadeAutonomaIncorporacao: '=',
                redirecionarInformarValorCub: '=',
                redirecionarInformarValorEdificacao: '=',
                tipoValorLaudo: '=',
                detalharLaudoRelatorio: '=',
                unidadeMedida: '=',
                areaTotalTerreno: '=',
                anexoLaudoRelatorio: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/resultados.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaTiposValores = [];
                vm.listaNivelFundamentacao = [];
                vm.ferramentasAnaliticas = [];
                vm.VALOR_MEDIO = 'VALOR_MEDIO';
                vm.VALOR_MINIMO = 'VALOR_MINIMO';
                vm.VALOR_MAXIMO = 'VALOR_MAXIMO';
                vm.OUTRO_VALOR = 'OUTRO_VALOR';

                vm.NAO_ATINGIDO = 'NAO_ATINGIDO';

                vm.CONTENT_TYPE_02 = 'CONTENT_TYPE_02';

                vm.LAUDO_RELATORIO = 'LAUDO_RELATORIO';
                vm.formatterValorMedio = null;
                vm.formatterValorMinimo = null;
                vm.formatterValorMaximo = null;
                vm.formatterOutroValorAdotado = null;

                vm.variacaoValorAdotado = null;
                vm.variacaoSuperiorValorMedio = null;
                vm.variacaoInferiorValorMedio = null;
                vm.valorAdotadoSelecionado = 0;

                vm.enquadramentoGrau3 = false;
                vm.enquadramentoGrau2 = false;
                vm.enquadramentoGrau1 = false;
                vm.enquadramentoGrauNaoAtingido = false;
                vm.relatoriosEstatisticos = [];

                var TRES = 'TRES';
                var DOIS = 'DOIS';
                var UM = 'UM';

                vm.icAdocaoEstimativa = true;

                var init = function () {
                    if(!vm.avaliacao.enquadramento){
                        vm.avaliacao.enquadramento = {};
                    }
                    if(vm.avaliacao.enquadramento.isApresentacaoLaudoModalidade === undefined){
                        vm.avaliacao.enquadramento.isApresentacaoLaudoModalidade = true;
                    }
                    if (!vm.areaTotalTerreno) {
                        vm.areaTotalTerreno = vm.laudoRelatorioAvaliacao.terrenoImovel.terreno.areaTotal;
                    }
                    vm.calcularAmplitude();
                    buscarTiposValores();
                    buscarFerramentasAnaliticas();
                    buscarMoedas();

                    if (!vm.avaliacao.anexos) {
                        vm.avaliacao.anexos = [];
                    }

                };

                vm.$watch('avaliacao', function () {
                    if (vm.avaliacao.resultadosAvaliacao) {
                        buscarMoedas(function () {
                            vm.calcularVariacaoValorAdotado();
                            for (var i = 0; i < vm.listaMoedas.length; i++) {
                                if (vm.avaliacao.resultadosAvaliacao.moeda) {
                                    if (vm.avaliacao.resultadosAvaliacao.moeda.id === vm.listaMoedas[i].id) {
                                        vm.avaliacao.resultadosAvaliacao.moeda = vm.listaMoedas[i];
                                    }
                                }

                            }
                            vm.validarValorImovel();
                        });
                    }
                });

                var buscarTiposValores = function () {
                    avaliacaoLaudoService.ordenacaoTipoValor().then(function (resp) {
                        vm.listaTiposValores = resp.data;
                    });
                };

                var buscarFerramentasAnaliticas = function () {
                    identificacaoLaudoRelatorioService.buscarFerramentasAnaliticas().then(function (resp) {
                        vm.ferramentasAnaliticas = resp.data;
                    });
                };

                var buscarMoedas = function (callback) {
                    avaliacaoLaudoService.getMoedas().then(function (resp) {
                        vm.listaMoedas = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };


                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService
                        .converterNumeroPorExtenso(numero, vm.avaliacao.resultadosAvaliacao.moeda.nome.toLowerCase());
                };

                vm.alterandoMoedaValores = function () {
                    if (vm.avaliacao.resultadosAvaliacao) {
                        vm.formatterValorMedio = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMedio);
                        vm.formatterValorMinimo = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMinimo);
                        vm.formatterValorMaximo = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMaximo);
                        vm.formatterOutroValorAdotado = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.outroValorAdotado);
                        vm.calcularVariacaoValorAdotado();
                    }
                };

                vm.calcularVariacaoValorAdotado = function () {
                    vm.determinarValorAdotadoSelecionado();
                    if (vm.avaliacao.resultadosAvaliacao.valorMedio) {
                        vm.variacaoSuperiorValorMedio = (vm.avaliacao.resultadosAvaliacao.valorMedio + (vm.avaliacao.resultadosAvaliacao.valorMedio * 0.01));
                        vm.variacaoInferiorValorMedio = (vm.avaliacao.resultadosAvaliacao.valorMedio - (vm.avaliacao.resultadosAvaliacao.valorMedio * 0.01));
                        vm.verificarAdocaoEstimativaValor();
                    }
                };

                vm.verificarAdocaoEstimativaValor = function () {
                    vm.icAdocaoEstimativa = true;
                    if ((vm.avaliacao.resultadosAvaliacao.tipoValor != vm.VALOR_MEDIO) && vm.valorAdotadoSelecionado && ( vm.valorAdotadoSelecionado < vm.variacaoInferiorValorMedio || vm.valorAdotadoSelecionado > vm.variacaoSuperiorValorMedio)) {
                        vm.icAdocaoEstimativa = false;
                    }
                };

                vm.determinarValorAdotadoSelecionado = function () {
                    if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MEDIO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMedio;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MINIMO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMinimo;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MAXIMO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMaximo;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.OUTRO_VALOR) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.outroValorAdotado;
                    }
                };

                vm.calcularAmplitude = function () {
                    var valor1 = 0;
                    var valor2 = 0;
                    if (vm.avaliacao.resultadosAvaliacao) {
                        if (vm.avaliacao.resultadosAvaliacao.valorMedio && vm.avaliacao.resultadosAvaliacao.valorMaximo && vm.avaliacao.resultadosAvaliacao.valorMinimo) {
                            valor1 = ((vm.avaliacao.resultadosAvaliacao.valorMedio - vm.avaliacao.resultadosAvaliacao.valorMinimo) * 100) / vm.avaliacao.resultadosAvaliacao.valorMedio;
                            valor2 = ((vm.avaliacao.resultadosAvaliacao.valorMaximo - vm.avaliacao.resultadosAvaliacao.valorMedio) * 100) / vm.avaliacao.resultadosAvaliacao.valorMaximo;
                            vm.avaliacao.enquadramento.amplitudeIntervalo = valor1 + valor2;
                        }
                    }
                };

                vm.validarValores = function () {

                    if (vm.avaliacao.resultadosAvaliacao.valorMinimo && vm.avaliacao.resultadosAvaliacao.valorMedio) {
                        if (vm.avaliacao.resultadosAvaliacao.valorMinimo > vm.avaliacao.resultadosAvaliacao.valorMedio) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-mininimo-maior-medio'));
                            vm.avaliacao.resultadosAvaliacao.valorMinimo = null;
                            return;
                        }
                    }
                    if (vm.avaliacao.resultadosAvaliacao.valorMedio && vm.avaliacao.resultadosAvaliacao.valorMaximo) {
                        if (vm.avaliacao.resultadosAvaliacao.valorMedio > vm.avaliacao.resultadosAvaliacao.valorMaximo) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-valor-médio-maior-maximo'));
                            vm.avaliacao.resultadosAvaliacao.valorMaximo = null;
                            return;
                        }
                    }
                };

                vm.medirGrauEnquadramentoFundamentacao = function () {
                    if (vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado
                        && vm.avaliacao.enquadramento.quantidadeMinimaDados
                        && vm.avaliacao.enquadramento.identificacaoDadosMercado
                        && vm.avaliacao.enquadramento.extrapolacao
                        && vm.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao
                        && vm.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido) {
                        if (vm.avaliacao.enquadramento.isApresentacaoLaudoModalidade
                            && vm.avaliacao.enquadramento.isApresentacaoAnaliseLaudo
                            && vm.avaliacao.enquadramento.isIdenticacaoCompletaEnderecos
                            && (vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado === TRES || vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado === DOIS)
                            && vm.avaliacao.enquadramento.quantidadeMinimaDados === TRES
                            && (vm.avaliacao.enquadramento.identificacaoDadosMercado === TRES || vm.avaliacao.enquadramento.identificacaoDadosMercado === DOIS)
                            && vm.avaliacao.enquadramento.extrapolacao === TRES
                            && vm.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao === TRES
                            && vm.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido === TRES) {
                            vm.enquadramentoGrau3 = true;
                            vm.enquadramentoGrau2 = false;
                            vm.enquadramentoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else if ((vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado === UM || vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado === DOIS || vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado === TRES)
                            && (vm.avaliacao.enquadramento.quantidadeMinimaDados === DOIS || vm.avaliacao.enquadramento.quantidadeMinimaDados === TRES)
                            && (vm.avaliacao.enquadramento.identificacaoDadosMercado === UM || vm.avaliacao.enquadramento.identificacaoDadosMercado === DOIS || vm.avaliacao.enquadramento.identificacaoDadosMercado === TRES)
                            && (vm.avaliacao.enquadramento.extrapolacao === DOIS || vm.avaliacao.enquadramento.extrapolacao === TRES)
                            && (vm.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao === DOIS || vm.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao === TRES)
                            && (vm.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido === DOIS || vm.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido === TRES)) {
                            vm.enquadramentoGrau3 = false;
                            vm.enquadramentoGrau2 = true;
                            vm.enquadramentoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else if (vm.avaliacao.enquadramento.caracterizacaoImovelAvaliado != vm.NAO_ATINGIDO
                            && vm.avaliacao.enquadramento.quantidadeMinimaDados != vm.NAO_ATINGIDO
                            && vm.avaliacao.enquadramento.identificacaoDadosMercado != vm.NAO_ATINGIDO
                            && vm.avaliacao.enquadramento.extrapolacao != vm.NAO_ATINGIDO
                            && vm.avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao != vm.NAO_ATINGIDO
                            && vm.avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido != vm.NAO_ATINGIDO) {
                            vm.enquadramentoGrau3 = false;
                            vm.enquadramentoGrau2 = false;
                            vm.enquadramentoGrau1 = true;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else {
                            vm.enquadramentoGrau3 = false;
                            vm.enquadramentoGrau2 = false;
                            vm.enquadramentoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = true;
                        }
                    }
                };

                vm.validarValorImovel = function () {
                    if (vm.avaliacao.tipoValorLaudo === 'INFORMAR_VALORES_LAUDO') {
                        if (!vm.valorAdotadoSelecionado) {
                            vm.valorAdotadoSelecionado = 0;
                        }
                        if (vm.avaliacao.valorTerreno > vm.valorAdotadoSelecionado || vm.avaliacao.valorTerreno < 0) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                            vm.avaliacao.valorTerreno = null;
                            for (i = 0; i < vm.listaBenfeitoriaAvaliacao.length; i++) {
                                vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.valor = 0;
                            }
                            return;
                        }
                    }else if(vm.avaliacao.tipoValorLaudo === 'ESTIMAR_VALORES'){
                        var total = 0;
                        if (!vm.valorAdotadoSelecionado) {
                            vm.valorAdotadoSelecionado = 0;
                        }

                        for (i = 0; i < vm.listaBenfeitoriaAvaliacao.length; i++) {
                            total += vm.listaBenfeitoriaAvaliacao[i].benfeitoriaImovel.valor;
                        }

                        if(total > vm.valorAdotadoSelecionado){
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-validar-valor-imovel'));
                        }
                    }
                };

                vm.limparDadosArquivosModelo = function () {
                    vm.avaliacao.resultadosAvaliacao.arquivosModelo = [];
                };

                init();
            }
        };
    };

})
();
(function () {

    directive.$inject = ["numeroExtensoAvaliacaoService", "ComumFactory", "cubAvaliacaoService", "$mdDialog"];
    angular.module('su-avaliacao').directive('resultadoAvaliacaoLaudoEvolutivo', directive);

    function directive(numeroExtensoAvaliacaoService, ComumFactory, cubAvaliacaoService, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                resultadoAvaliacao: '=',
                quantificacaoCusto: '=',
                listaBenfeitoriaAvaliacao: '=',
                avaliacao: '=',
                formularioValidacao: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '=',
                identificacao: '=',
                enquadramento: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/resultadoAvaliacaoLaudoEvolutivo.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.VALOR_MEDIO = 'VALOR_MEDIO';
                vm.VALOR_MINIMO = 'VALOR_MINIMO';
                vm.VALOR_MAXIMO = 'VALOR_MAXIMO';
                vm.OUTRO_VALOR = 'OUTRO_VALOR';
                vm.OUTRA_FONTE = 'OUTRA_FONTE';

                vm.enquadramentoGrau1 = false;
                vm.enquadramentoGrau2 = false;
                vm.enquadramentoGrau3 = false;
                vm.itemEstimativaValorTerrenoGrau3 = false;
                vm.itemEstimativaValorTerrenoGrau2 = false;
                vm.itemEstimativaValorTerrenoGrau1 = false;
                vm.itemEstimativaCustosReedicaoGrau3 = false;
                vm.itemEstimativaCustosReedicaoGrau2 = false;
                vm.itemEstimativaCustosReedicaoGrau1 = false;
                vm.itemFatorComercializacaoGrau3 = false;
                vm.itemFatorComercializacaoGrau2 = false;
                vm.itemFatorComercializacaoGrau1 = false;

                var TRES = 'TRES';
                var DOIS = 'DOIS';
                var UM = 'UM';

                vm.colorTableColumnAvaliacao = {
                    'background-color': 'lightgreen'
                };

                vm.listaTiposFatorComercializacao = {
                    INFERIDO_MERCADO_SEMELHANTE: "Inferido em mercado Semelhante",
                    JUSTIFICADO: "Justificado",
                    ARBITRADO: "Arbitrado"
                };


                vm.$watch('quantificacaoCusto', function () {
                    if (vm.quantificacaoCusto) {
                        angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                            if (benfeitoria.valorCalculado) {
                                benfeitoria.formatterValorCalculadoBenfeitoria = vm.convertNumeroPorExtenso(benfeitoria.valorCalculado);
                            }
                        });
                    }
                });

                vm.$watch('resultadoAvaliacao', function () {
                    if (vm.resultadoAvaliacao) {
                        vm.calcularValorImovel();
                        if (vm.resultadoAvaliacao.valorImovel > 0) {
                            vm.calcularGrauFundamentacaoMetodoEvolutivo();
                        }

                    }
                });

                vm.$watch('avaliacao', function () {
                    if (vm.avaliacao) {
                        if (vm.identificacao.metodologia !== 'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA') {
                            vm.determinarValorAdotadoSelecionado();
                        } else {
                            vm.valorTerreno = vm.avaliacao.valorTerreno;
                        }
                        if (vm.valorTerreno) {
                            vm.formatterValorTerreno = vm.convertNumeroPorExtenso(vm.valorTerreno);
                        }
                    }

                    if (vm.avaliacao.tipoValorTerrenoAvaliacao === vm.OUTRA_FONTE) {
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                            if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao !== 'Complementar') {
                                if (benfeitoria.benfeitoriaImovel.valorEdificacao && benfeitoria.benfeitoriaImovel.valorEdificacao.valor) {
                                    benfeitoria.benfeitoriaImovel.valorEdificacao.formatterValorEdificacao = vm.convertNumeroPorExtenso(benfeitoria.benfeitoriaImovel.valorEdificacao.valor);
                                }
                            }
                        });

                    }
                });

                var init = function () {

                };

                vm.determinarValorAdotadoSelecionado = function () {
                    if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MEDIO) {
                        vm.valorTerreno = vm.avaliacao.resultadosAvaliacao.valorMedio;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MINIMO) {
                        vm.valorTerreno = vm.avaliacao.resultadosAvaliacao.valorMinimo;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MAXIMO) {
                        vm.valorTerreno = vm.avaliacao.resultadosAvaliacao.valorMaximo;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.OUTRO_VALOR) {
                        vm.valorTerreno = vm.avaliacao.resultadosAvaliacao.outroValorAdotado;
                    }
                };

                vm.calcularValorImovel = function () {

                    if (vm.valorTerreno && vm.quantificacaoCusto.benfeitorias && vm.resultadoAvaliacao.valorFc) {
                        vm.resultadoAvaliacao.valorImovel = vm.valorTerreno;
                        angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                            vm.resultadoAvaliacao.valorImovel += benfeitoria.valorCalculado;
                        });

                        vm.resultadoAvaliacao.valorImovel *= vm.resultadoAvaliacao.valorFc;
                        if (vm.resultadoAvaliacao.valorImovel) {
                            vm.formatterValorImovel = vm.convertNumeroPorExtenso(vm.resultadoAvaliacao.valorImovel);
                        }
                    }
                };

                vm.calcularGrauFundamentacaoMetodoEvolutivo = function () {

                    if (vm.valorTerreno && vm.resultadoAvaliacao.valorImovel > 0) {
                        if (vm.valorTerreno < (vm.resultadoAvaliacao.valorImovel * 0.15)) {
                            vm.itemEstimativaValorTerrenoGrau2 = true;
                        } else {
                            vm.medirGrauEnquadramentoFundamentacao();
                        }
                        if ((vm.resultadoAvaliacao.valorImovel - vm.valorTerreno) < (vm.resultadoAvaliacao.valorImovel * 0.15)) {
                            vm.itemEstimativaCustosReedicaoGrau2 = true;
                        } else {
                            vm.calcularEnquadramentoGrauFundamentacao();
                        }
                    }

                    if (vm.resultadoAvaliacao.tipoFatorComercializacao === 'INFERIDO_MERCADO_SEMELHANTE') {
                        vm.itemFatorComercializacaoGrau3 = true;
                        vm.itemFatorComercializacaoGrau2 = false;
                        vm.itemFatorComercializacaoGrau1 = false;
                    } else if (vm.resultadoAvaliacao.tipoFatorComercializacao === 'JUSTIFICADO') {
                        vm.itemFatorComercializacaoGrau3 = false;
                        vm.itemFatorComercializacaoGrau2 = true;
                        vm.itemFatorComercializacaoGrau1 = false;
                    } else if (vm.resultadoAvaliacao.tipoFatorComercializacao === 'ARBITRADO') {
                        vm.itemFatorComercializacaoGrau3 = false;
                        vm.itemFatorComercializacaoGrau2 = false;
                        vm.itemFatorComercializacaoGrau1 = true;
                    }

                    vm.calcularEnquadramentoGrauFundamentacaoMetodoEvolutivo();
                };

                vm.calcularEnquadramentoGrauFundamentacaoMetodoEvolutivo = function () {
                    if (vm.itemEstimativaValorTerrenoGrau3 && vm.itemEstimativaCustosReedicaoGrau3 && (vm.itemFatorComercializacaoGrau3 || vm.itemFatorComercializacaoGrau2)) {
                        vm.enquadramentoGrau3 = true;
                    } else if ((vm.itemEstimativaValorTerrenoGrau2 || vm.itemEstimativaValorTerrenoGrau3) && (vm.itemEstimativaCustosReedicaoGrau3 || vm.itemEstimativaCustosReedicaoGrau2)) {
                        vm.enquadramentoGrau2 = true;
                    } else if ((vm.itemEstimativaValorTerrenoGrau3 || vm.itemEstimativaValorTerrenoGrau2 || vm.itemEstimativaValorTerrenoGrau1) &&
                        (vm.itemEstimativaCustosReedicaoGrau3 || vm.itemEstimativaCustosReedicaoGrau2 || vm.itemEstimativaCustosReedicaoGrau1) &&
                        (vm.itemFatorComercializacaoGrau3 || vm.itemFatorComercializacaoGrau2 || vm.itemFatorComercializacaoGrau1)) {
                        vm.enquadramentoGrau1 = true;
                    }
                };

                vm.calcularEnquadramentoGrauFundamentacao = function () {
                    if (vm.identificacao.quantificacaoCustoBenfeitoria && vm.quantificacaoCusto && vm.quantificacaoCusto.tipoBdi && vm.quantificacaoCusto.tipoDepreciacao) {
                        if (vm.identificacao.quantificacaoCustoBenfeitoria === 'ORCAMENTO' && vm.quantificacaoCusto.tipoBdi !== 'ARBITRADO' && vm.quantificacaoCusto.tipoDepreciacao !== 'ARBITRADO') {
                            vm.itemEstimativaCustosReedicaoGrau3 = true;
                        } else if (vm.identificacao.quantificacaoCustoBenfeitoria !== 'CUB_PROJETO_DIFERENTE' && vm.quantificacaoCusto.tipoBdi !== 'ARBITRADO') {
                            vm.itemEstimativaCustosReedicaoGrau2 = true;
                        } else {
                            vm.itemEstimativaCustosReedicaoGrau1 = true;
                        }
                    }
                };

                vm.medirGrauEnquadramentoFundamentacao = function () {
                    if (vm.enquadramento.caracterizacaoImovelAvaliado
                        && vm.enquadramento.quantidadeMinimaDados
                        && vm.enquadramento.identificacaoDadosMercado
                        && vm.enquadramento.extrapolacao
                        && vm.enquadramento.nivelSignificanciaMaximoRejeicao
                        && vm.enquadramento.nivelSignificanciaMaximoAdmitido) {
                        if (vm.enquadramento.isApresentacaoLaudoModalidade
                            && vm.enquadramento.isApresentacaoAnaliseLaudo
                            && vm.enquadramento.isIdenticacaoCompletaEnderecos
                            && (vm.enquadramento.caracterizacaoImovelAvaliado === TRES || vm.enquadramento.caracterizacaoImovelAvaliado === DOIS)
                            && vm.enquadramento.quantidadeMinimaDados === TRES
                            && (vm.enquadramento.identificacaoDadosMercado === TRES || vm.enquadramento.identificacaoDadosMercado === DOIS)
                            && vm.enquadramento.extrapolacao === TRES
                            && vm.enquadramento.nivelSignificanciaMaximoRejeicao === TRES
                            && vm.enquadramento.nivelSignificanciaMaximoAdmitido === TRES) {
                            vm.itemEstimativaValorTerrenoGrau3 = true;
                            vm.itemEstimativaValorTerrenoGrau2 = false;
                            vm.itemEstimativaValorTerrenoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else if ((vm.enquadramento.caracterizacaoImovelAvaliado === UM || vm.enquadramento.caracterizacaoImovelAvaliado === DOIS || vm.enquadramento.caracterizacaoImovelAvaliado === TRES)
                            && (vm.enquadramento.quantidadeMinimaDados === DOIS || vm.enquadramento.quantidadeMinimaDados === TRES)
                            && (vm.enquadramento.identificacaoDadosMercado === UM || vm.enquadramento.identificacaoDadosMercado === DOIS || vm.enquadramento.identificacaoDadosMercado === TRES)
                            && (vm.enquadramento.extrapolacao === DOIS || vm.enquadramento.extrapolacao === TRES)
                            && (vm.enquadramento.nivelSignificanciaMaximoRejeicao === DOIS || vm.enquadramento.nivelSignificanciaMaximoRejeicao === TRES)
                            && (vm.enquadramento.nivelSignificanciaMaximoAdmitido === DOIS || vm.enquadramento.nivelSignificanciaMaximoAdmitido === TRES)) {
                            vm.itemEstimativaValorTerrenoGrau3 = false;
                            vm.itemEstimativaValorTerrenoGrau2 = true;
                            vm.itemEstimativaValorTerrenoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else if (vm.enquadramento.caracterizacaoImovelAvaliado !== vm.NAO_ATINGIDO
                            && vm.enquadramento.quantidadeMinimaDados !== vm.NAO_ATINGIDO
                            && vm.enquadramento.identificacaoDadosMercado !== vm.NAO_ATINGIDO
                            && vm.enquadramento.extrapolacao !== vm.NAO_ATINGIDO
                            && vm.enquadramento.nivelSignificanciaMaximoRejeicao !== vm.NAO_ATINGIDO
                            && vm.enquadramento.nivelSignificanciaMaximoAdmitido !== vm.NAO_ATINGIDO) {
                            vm.itemEstimativaValorTerrenoGrau3 = false;
                            vm.itemEstimativaValorTerrenoGrau2 = false;
                            vm.itemEstimativaValorTerrenoGrau1 = true;
                            vm.enquadramentoGrauNaoAtingido = false;
                        } else {
                            vm.itemEstimativaValorTerrenoGrau3 = false;
                            vm.itemEstimativaValorTerrenoGrau2 = false;
                            vm.itemEstimativaValorTerrenoGrau1 = false;
                            vm.enquadramentoGrauNaoAtingido = true;
                        }
                    }
                };

                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService
                        .converterNumeroPorExtenso(numero, 'real');
                };

                init();
            }
        };
    }

})();
(function () {

    directive.$inject = ["numeroExtensoAvaliacaoService", "ComumFactory", "cubAvaliacaoService", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('quantificacaoCustoCubLaudo', directive);

    function directive(numeroExtensoAvaliacaoService, ComumFactory, cubAvaliacaoService, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                quantificacaoCusto: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '=',
                formularioValidacao: '=',
                listaBenfeitoriaAvaliacao: '=',
                identificacao: '=',
                uf: '=',
                redirecionarCalcularAreaEquivalente: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/quantificacaoCub.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.enquadramentoGrau1 = false;
                vm.enquadramentoGrau2 = false;
                vm.enquadramentoGrau3 = false;

                vm.colorTableColumnAvaliacao = {
                    'background-color': 'lightgreen'
                };

                vm.listaTiposDbi = {
                    CALCULADO: "Calculado",
                    JUSTIFICADO: "Justificado",
                    ARBITRADO: "Arbitrado"
                };

                vm.listaTiposDepreciacao = {
                    CUSTO_RECUPERACAO: "Custo de Recuperação",
                    ROSS_HEIDECKE: "Ross Heidecke",
                    ARBITRADO: "Arbitrado"
                };

                vm.listaTiposArea = {
                    AREA_REAL: "Área Real",
                    AREA_EQUIVALENTE_CONSTRUCAO: "Área Equivalente de Construção"
                };

                vm.listaTiposEstimativaCusto = {
                    UTILIZAR_ORCAMENTOS: "Utilizar Orçamentos",
                    APLICAR_PERCENTUAL: "Aplicar Percentual"
                };

                var init = function () {
                    pesquisarEstadosConservacao();
                    preencheTiposCub();
                    populaTabelaDepreciacao();
                };

                vm.$watch('identificacao', function () {
                    if (vm.identificacao.quantificacaoCustoBenfeitoria) {
                        vm.calcularEnquadramentoGrauFundamentacao();
                    }
                });

                vm.$watch('quantificacaoCusto', function () {
                    if (vm.quantificacaoCusto) {
                        if (!vm.quantificacaoCusto.benfeitorias || vm.quantificacaoCusto.benfeitorias.length === 0) {
                            vm.quantificacaoCusto.benfeitorias = [];
                            angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                                if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao !== 'Complementar') {
                                    vm.quantificacaoCusto.benfeitorias.push({
                                        idBenfeitoriaImovel: benfeitoria.benfeitoriaImovel.id,
                                        idOriginal: benfeitoria.benfeitoriaImovel.idOriginal,
                                        codigo: benfeitoria.benfeitoriaImovel.codigo,
                                        nome: benfeitoria.benfeitoriaImovel.nome,
                                        tipoBenfeitoria: benfeitoria.benfeitoriaImovel.tipoBenfeitoria,
                                        area: benfeitoria.benfeitoriaImovel.areaConstruida
                                    });
                                }
                            });
                        } else if (vm.quantificacaoCusto.benfeitorias.length > 0) {
                            pesquisarEstadosConservacao(function () {
                                preencheTiposCub(function () {
                                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                                        if (benfeitoria.estadoConservacao) {
                                            for (var i = 0; i < vm.listaEstadosConservacao.length; i++) {
                                                if (vm.listaEstadosConservacao[i].id === benfeitoria.estadoConservacao.id) {
                                                    benfeitoria.estadoConservacao = vm.listaEstadosConservacao[i];
                                                }
                                            }
                                        }
                                    });
                                });
                                vm.calcularCustoConstrucao();
                            });
                        }
                    }
                });


                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService
                        .converterNumeroPorExtenso(numero, 'real');
                };


                vm.calcularValorBenfeitoria = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        if (vm.quantificacaoCusto.valorTaxaDepreciacao) {
                            benfeitoria.valorCalculado = ((benfeitoria.area * benfeitoria.valorCustoConstrucao) * (1 + (vm.quantificacaoCusto.valorTaxaBdi / 100)) * (1 - (vm.quantificacaoCusto.valorTaxaDepreciacao / 100)));
                        } else {
                            benfeitoria.valorCalculado = ((benfeitoria.area * benfeitoria.valorCustoConstrucao) * (1 + (vm.quantificacaoCusto.valorTaxaBdi / 100)) * (1 - (benfeitoria.valorTaxaDepreciacao / 100)));
                        }

                        benfeitoria.formatterValorCalculadoBenfeitoria = vm.convertNumeroPorExtenso(benfeitoria.valorCalculado);
                    });

                    vm.valorCustoTotalBenfeitoria = 0;
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        vm.valorCustoTotalBenfeitoria += benfeitoria.valorCalculado;
                    });

                    vm.formatterValorCustoTotalBenfeitoria = vm.convertNumeroPorExtenso(vm.valorCustoTotalBenfeitoria);
                };

                var buscarValoresCubSiapa = function (tipoCub, callback) {
                    var date = new Date();

                    for (var i = 0; i < vm.tiposCub.length; i++) {
                        if (vm.tiposCub[i].nome === tipoCub) {
                            tipoCub = vm.tiposCub[i];
                        }
                    }

                    cubAvaliacaoService.buscarCubSiapa(vm.uf, date, tipoCub).then(function (resp) {
                        vm.valorCub = tipoCub.nome === 'CUB Médio' ? resp.data.cubs[0].valorMedio : resp.data.cubs[0].valor;
                        if (callback) {
                            callback();
                        }
                    }, function () {
                        vm.valorCub = null;
                    });
                };

                vm.calcularCustoConstrucao = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        if (vm.quantificacaoCusto.tipoArea === 'AREA_REAL') {
                            if (benfeitoria.cubDepreciacao != null && benfeitoria.valorOrcamentoElevadores != null && benfeitoria.valorOrcamentoInstalacoesEspeciais != null && benfeitoria.valorOrcamentoFundacoesEspeciais != null && benfeitoria.area != null) {
                                buscarValoresCubSiapa(benfeitoria.cubDepreciacao, function () {
                                    if (vm.valorCub) {
                                        if (vm.quantificacaoCusto.tipoEstimativaCusto === 'UTILIZAR_ORCAMENTOS') {
                                            benfeitoria.valorCustoConstrucao = vm.valorCub + ((benfeitoria.valorOrcamentoElevadores + benfeitoria.valorOrcamentoInstalacoesEspeciais + (benfeitoria.valorOrcamentoFundacoesEspeciais - benfeitoria.valorOrcamentoFundacoesDiretas)) / benfeitoria.area);
                                        } else {
                                            benfeitoria.valorCustoConstrucao = vm.valorCub * (1 + (benfeitoria.valorOrcamentoElevadores / 100)) * (1 + (benfeitoria.valorOrcamentoInstalacoesEspeciais / 100)) * (1 + (benfeitoria.valorOrcamentoFundacoesEspeciais / 100));
                                        }
                                        vm.calcularValorBenfeitoria();
                                    }
                                });

                            }
                        } else {
                            if (benfeitoria.areaEquivalente != null && benfeitoria.areaEquivalente.cubPadrao != null && benfeitoria.valorOrcamentoElevadores != null && benfeitoria.valorOrcamentoInstalacoesEspeciais != null && benfeitoria.valorOrcamentoFundacoesEspeciais != null && benfeitoria.areaEquivalente.areaConstruidaPadrao != null) {
                                buscarValoresCubSiapa(benfeitoria.areaEquivalente.cubPadrao, function () {
                                    if (vm.valorCub) {
                                        if (vm.quantificacaoCusto.tipoEstimativaCusto === 'UTILIZAR_ORCAMENTOS') {
                                            benfeitoria.valorCustoConstrucao = vm.valorCub + ((benfeitoria.valorOrcamentoElevadores + benfeitoria.valorOrcamentoInstalacoesEspeciais + (benfeitoria.valorOrcamentoFundacoesEspeciais - benfeitoria.valorOrcamentoFundacoesDiretas)) / benfeitoria.areaEquivalente.areaConstruidaPadrao);
                                        } else {
                                            benfeitoria.valorCustoConstrucao = vm.valorCub * (1 + (benfeitoria.valorOrcamentoElevadores / 100)) * (1 + (benfeitoria.valorOrcamentoInstalacoesEspeciais / 100)) * (1 + (benfeitoria.valorOrcamentoFundacoesEspeciais / 100));
                                        }
                                        vm.calcularValorBenfeitoria();
                                    }
                                });

                            }
                        }
                    });
                };

                vm.calcularEnquadramentoGrauFundamentacao = function () {
                    vm.enquadramentoGrau1 = false;
                    vm.enquadramentoGrau2 = false;
                    vm.enquadramentoGrau3 = false;

                    if (vm.identificacao.quantificacaoCustoBenfeitoria && vm.quantificacaoCusto && vm.quantificacaoCusto.tipoBdi && vm.quantificacaoCusto.tipoDepreciacao) {
                        if (vm.identificacao.quantificacaoCustoBenfeitoria === 'ORCAMENTO' && vm.quantificacaoCusto.tipoBdi !== 'ARBITRADO' && vm.quantificacaoCusto.tipoDepreciacao !== 'ARBITRADO') {
                            vm.enquadramentoGrau3 = true;
                        } else if (vm.identificacao.quantificacaoCustoBenfeitoria !== 'CUB_PROJETO_DIFERENTE' && vm.quantificacaoCusto.tipoBdi !== 'ARBITRADO') {
                            vm.enquadramentoGrau2 = true;
                        } else {
                            vm.enquadramentoGrau1 = true;
                        }
                    }
                };

                vm.calcularAreaEquilente = function (benfeitoria, detalhar) {
                    vm.redirecionarCalcularAreaEquivalente(benfeitoria, detalhar, 'QUANTIFICACAO');
                };

                vm.limparBenfeitoriaCub = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        benfeitoria.cubDepreciacao = null;
                        benfeitoria.areaEquivalente = null;
                    });
                };

                vm.limparDadosBenfeitoriaDepreciacao = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        benfeitoria.vidaUtil = null;
                        benfeitoria.idadeAparente = null;
                        benfeitoria.estadoConservacao = null;
                        benfeitoria.valorTaxaDepreciacao = null;
                    });
                };

                vm.calcularValorEstimado = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        if (verificaIdadeAparente(benfeitoria.idadeAparente, benfeitoria.vidaUtil) && benfeitoria.estadoConservacao) {
                            if (benfeitoria.idadeAparente !== 0) {
                                var porcentagemVida = (benfeitoria.idadeAparente * 100) / benfeitoria.vidaUtil;
                                porcentagemVida = Math.round(porcentagemVida * 100) / 100;

                                porcentagemVida = arredondaNumero(porcentagemVida);

                                var valorSubtracao = benfeitoria.estadoConservacao.id === 9 ? 2 : 1;
                                benfeitoria.valorTaxaDepreciacao = vm.tabelaDepreciacao['coluna' + porcentagemVida][benfeitoria.estadoConservacao.id - valorSubtracao];
                            }
                        }
                    });
                };

                function arredondaNumero(porcentagemVida) {
                    var valor = 2 * Math.round(porcentagemVida / 2);

                    if (valor < 2) {
                        return 2;
                    } else if (valor > 100) {
                        return 100;
                    } else if (valor % 2 !== 0) {
                        return valor + 1;
                    } else {
                        return valor;
                    }

                }

                var pesquisarEstadosConservacao = function (callback) {
                    cubAvaliacaoService.buscarEstadosConservacao().then(function (resp) {
                        vm.listaEstadosConservacao = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                function verificaIdadeAparente(idadeAparente, vidaUtil) {
                    if (idadeAparente > vidaUtil) {
                        vidaUtil = '';
                        idadeAparente = '';
                        ComumFactory.mostrarMensagemError('A idade aparente do imóvel não pode ser maior que a vida útil.');
                        return false;
                    } else if (vidaUtil === 0) {
                        vidaUtil = '';
                        ComumFactory.mostrarMensagemError('A Vida Útil não pode ser igual a 0');
                        return false;
                    }
                    return true;
                }

                vm.showDialogDepreciacaoHeidecke = function (ev) {
                    $mdDialog.show({
                        controller: function () {

                            vm.downloadPlanilha = function () {
                                window.open('assets/docs/Tab_Ross.pdf', "_blank");
                            }

                            return vm;
                        },
                        controllerAs: 'vm',
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDepreciacaoHeidecke.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: true
                    });
                };

                var preencheTiposCub = function (callback) {
                    vm.tiposCub = [];
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Baixo)', 1, 1, 'BAIXO', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Normal)', 1, 2, 'NORMAL', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Alto)', 1, 3, 'ALTO', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Baixo)', 4, 1, 'BAIXO', 'PP4_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Normal)', 4, 2, 'NORMAL', 'PP4_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Baixo)', 2, 1, 'BAIXO', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Normal)', 2, 2, 'NORMAL', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Alto)', 2, 3, 'ALTO', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Normal)', 3, 2, 'NORMAL', 'R16_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Alto)', 3, 3, 'ALTO', 'R16_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PIS, Padrão Residencial, Projeto de interesse social (Baixo)', 10, 1, 'BAIXO', 'PIS_PROJETO_INTERESSE_SOCIAL'));
                    vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Normal)', 5, 2, 'NORMAL', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
                    vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Alto)', 5, 3, 'ALTO', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
                    vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Normal)', 6, 2, 'NORMAL', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Alto)', 6, 3, 'ALTO', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Normal)', 7, 2, 'NORMAL', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Alto)', 7, 3, 'ALTO', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('GI, Padrão Galpão Indústrial, 1 pavimento', 8, 1, 'BAIXO', 'GI_PADRAO_GALPAO_INDUSTRIAL'));
                    vm.tiposCub.push(new TipoCub('RP1Q, Padrão Residencia Popular, 1 pavimento', 9, 1, 'BAIXO', 'RP1Q_PADRAO_RESIDENCIAL_POPULAR_QUARTO'));
                    vm.tiposCub.push(new TipoCub('CUB Médio', null, null, 'CUB_MEDIO', 'CUB_MEDIO'));

                    function TipoCub(nome, tipo, padrao, padraoAcabamento, tipoProjetoCub) {
                        this.nome = nome;
                        this.tipo = tipo;
                        this.padrao = padrao;
                        this.padraoAcabamento = padraoAcabamento;
                        this.tipoProjetoCub = tipoProjetoCub;
                    }

                    if (callback) {
                        callback();
                    }
                };

                function populaTabelaDepreciacao() {
                    vm.tabelaDepreciacao = {
                        coluna2: [1.02, 1.05, 3.51, 9.03, 18.9, 33.9, 53.1, 75.4],
                        coluna4: [2.08, 2.11, 4.55, 10, 19.8, 34.6, 53.6, 75.7],
                        coluna6: [3.18, 3.21, 5.62, 11, 20.7, 35.3, 54.1, 76],
                        coluna8: [4.32, 4.35, 6.73, 12.1, 21.6, 36.1, 54.6, 76.3],
                        coluna10: [5.5, 5.53, 7.88, 13.2, 22.6, 36.9, 55.2, 76.6],
                        coluna12: [6.72, 6.75, 9.07, 14.3, 23.6, 37.7, 55.8, 76.9],
                        coluna14: [7.98, 8.01, 10.3, 15.4, 24.6, 38.5, 56.4, 77.2],
                        coluna16: [9.28, 9.31, 11.6, 16.6, 25.7, 39.4, 57, 77.5],
                        coluna18: [10.6, 10.6, 12.9, 17.8, 26.8, 40.3, 57.6, 77.8],
                        coluna20: [12, 12, 14.2, 19.1, 27.9, 42.2, 58.3, 78.2],
                        coluna22: [13.4, 13.4, 15.6, 20.4, 29.1, 42.2, 59, 78.5],
                        coluna24: [14.9, 14.9, 17, 21.8, 30.3, 43.1, 59.6, 78.9],
                        coluna26: [16.4, 16.4, 18.5, 23.1, 31.5, 44.1, 60.4, 79.3],
                        coluna28: [17.9, 17, 20, 24.6, 32.8, 45.2, 61.1, 79.6],
                        coluna30: [19.5, 19.5, 21.5, 26, 34.1, 46.2, 61.8, 80],
                        coluna32: [21.1, 21.1, 23.1, 27.5, 35.4, 47.3, 62.6, 80.4],
                        coluna34: [22.8, 22.8, 24.7, 29, 36.8, 48.4, 63.4, 80.8],
                        coluna36: [24.5, 24.5, 26.4, 30.5, 38.1, 49.5, 64.2, 81.3],
                        coluna38: [26.2, 26.2, 28.1, 32.2, 39.6, 50.7, 65, 81.7],
                        coluna40: [28.8, 28.8, 29.9, 33.8, 41, 51.9, 65.9, 82.1],
                        coluna42: [29.8, 29.8, 31.6, 35.5, 42.5, 53.1, 66.7, 82.6],
                        coluna44: [31.7, 31.7, 33.4, 37.2, 44, 54.4, 67.6, 83.1],
                        coluna46: [33.6, 33.6, 35.2, 38.9, 45.6, 55.6, 68.5, 83.5],
                        coluna48: [35.5, 35.5, 37.1, 40.7, 47.2, 56.9, 69.4, 84],
                        coluna50: [37.5, 37.5, 39.1, 42.6, 48.8, 58.2, 70.4, 84.5],
                        coluna52: [39.5, 39.5, 41.9, 44, 50.5, 59.6, 71.3, 85],
                        coluna54: [41.6, 41.6, 43, 46.3, 52.1, 61, 72.3, 85.5],
                        coluna56: [43.7, 43.7, 45.1, 48.2, 53.9, 62.4, 63.3, 86],
                        coluna58: [45.8, 45.8, 47.2, 50.2, 55.6, 63.8, 74.3, 86.6],
                        coluna60: [48.8, 48.8, 49.3, 52.2, 57.4, 65.3, 75.3, 87.1],
                        coluna62: [50.2, 50.2, 51.5, 54.2, 59.2, 66.7, 75.4, 87.7],
                        coluna64: [52.5, 52.5, 53.7, 56.3, 61.1, 61.3, 77.5, 88.2],
                        coluna66: [54.8, 54.8, 55.9, 58.4, 69, 69.8, 78.6, 88.8],
                        coluna68: [57.1, 57.1, 58.2, 60.6, 64.9, 71.4, 79.7, 89.4],
                        coluna70: [59.5, 59.5, 60.5, 62.8, 66.8, 72.9, 80.8, 90.8],
                        coluna72: [62.2, 61.9, 62.9, 85, 68.8, 74.6, 81.9, 90.6],
                        coluna74: [64.4, 64.4, 65.3, 67.3, 70.8, 76.2, 83.1, 91.2],
                        coluna76: [66.9, 66.9, 67.7, 69.6, 72.9, 77.9, 84.3, 91.8],
                        coluna78: [69.4, 69.4, 72.7, 71.9, 74.9, 89.6, 85.5, 92.4],
                        coluna80: [72, 72, 72.7, 74.3, 77.1, 81.3, 86.7, 93.1],
                        coluna82: [74.6, 74.6, 75.3, 76.7, 79.2, 83, 88, 93.7],
                        coluna84: [77.3, 77.3, 77.8, 79.1, 81.4, 84.8, 89.2, 94.4],
                        coluna86: [80, 80, 80.5, 81.6, 83.6, 86.6, 90.5, 95],
                        coluna88: [82.7, 82.7, 83.2, 84.1, 85.8, 88.5, 91.8, 95.7],
                        coluna90: [85.5, 85.5, 85.9, 86.7, 88.1, 90.3, 93.1, 96.4],
                        coluna92: [88.3, 83.3, 88.6, 89.3, 90.4, 92.7, 94.5, 97.1],
                        coluna94: [91.2, 91.2, 91.4, 91.9, 92.8, 94.1, 95.8, 97.8],
                        coluna96: [94.1, 94.1, 94.2, 94.6, 95.1, 96, 97.2, 98.5],
                        coluna98: [97, 97, 97.1, 97.3, 97.6, 98, 98, 99.8],
                        coluna100: [100, 100, 100, 100, 100, 100, 100, 100]
                    };
                };

                vm.cancelModal = function () {
                    $mdDialog.cancel();
                };

                init();
            }
        };
    };

})
();
(function () {

    directive.$inject = ["avaliacaoLaudoService", "numeroExtensoAvaliacaoService", "ComumFactory", "cubAvaliacaoService", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('quantificacaoCustoLaudo', directive);

    function directive(avaliacaoLaudoService, numeroExtensoAvaliacaoService, ComumFactory, cubAvaliacaoService, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                quantificacaoCusto: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '=',
                formularioValidacao: '=',
                listaBenfeitoriaAvaliacao: '=',
                identificacao: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/quantificacao.tmpl.html',
            link: function ($scope) {

                var vm = $scope;


                vm.enquadramentoGrau1 = false;
                vm.enquadramentoGrau2 = false;
                vm.enquadramentoGrau3 = false;

                vm.colorTableColumnAvaliacao = {
                    'background-color': 'lightgreen'
                };

                vm.listaTiposDbi = {
                    CALCULADO: "Calculado",
                    JUSTIFICADO: "Justificado",
                    ARBITRADO: "Arbitrado"
                };

                vm.listaTiposDepreciacao = {
                    CUSTO_RECUPERACAO: "Custo de Recuperação",
                    ROSS_HEIDECKE: "Ross Heidecke",
                    ARBITRADO: "Arbitrado"
                };

                var init = function () {
                    pesquisarEstadosConservacao();
                    preencheTiposCub();
                    populaTabelaDepreciacao();


                };

                vm.$watch('identificacao', function () {
                    if (vm.identificacao.quantificacaoCustoBenfeitoria) {
                        vm.calcularEnquadramentoGrauFundamentacao();
                    }
                });

                vm.$watch('quantificacaoCusto', function () {
                    if (vm.quantificacaoCusto) {
                        if (!vm.quantificacaoCusto.benfeitorias || vm.quantificacaoCusto.benfeitorias.length === 0) {
                            vm.quantificacaoCusto.benfeitorias = [];
                            angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                                if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao !== 'Complementar') {
                                    vm.quantificacaoCusto.benfeitorias.push({
                                        idBenfeitoriaImovel: benfeitoria.benfeitoriaImovel.id,
                                        idOriginal: benfeitoria.benfeitoriaImovel.idOriginal,
                                        codigo: benfeitoria.benfeitoriaImovel.codigo,
                                        nome: benfeitoria.benfeitoriaImovel.nome,
                                        tipoBenfeitoria: benfeitoria.benfeitoriaImovel.tipoBenfeitoria,
                                        area: benfeitoria.benfeitoriaImovel.areaConstruida
                                    });
                                }
                            });
                        } else if (vm.quantificacaoCusto.benfeitorias.length > 0) {
                            pesquisarEstadosConservacao(function () {
                                preencheTiposCub(function () {
                                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                                        if (benfeitoria.estadoConservacao) {
                                            for (var i = 0; i < vm.listaEstadosConservacao.length; i++) {
                                                if (vm.listaEstadosConservacao[i].id === benfeitoria.estadoConservacao.id) {
                                                    benfeitoria.estadoConservacao = vm.listaEstadosConservacao[i];
                                                }
                                            }
                                        }
                                    });
                                    vm.calcularValorBenfeitoria();
                                    vm.calcularTotalvalorCustoReproducao();
                                });
                            });

                        }
                    }
                });


                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService
                        .converterNumeroPorExtenso(numero, 'real');
                };

                vm.calcularTotalvalorCustoReproducao = function () {
                    vm.valorCustoReproducaoTotal = 0;
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        vm.valorCustoReproducaoTotal = vm.valorCustoReproducaoTotal + benfeitoria.valorCustoReproducao;
                        if (benfeitoria.valorCustoReproducao) {
                            benfeitoria.formatterValorCustoReproducao = vm.convertNumeroPorExtenso(benfeitoria.valorCustoReproducao);
                        }
                    });
                    if (vm.valorCustoReproducaoTotal) {
                        vm.formatterValorCustoReproducaoTotal = vm.convertNumeroPorExtenso(vm.valorCustoReproducaoTotal);
                    }
                };

                vm.calcularValorBenfeitoria = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        if (vm.quantificacaoCusto.valorTaxaDepreciacao) {
                            if (vm.quantificacaoCusto.isTaxaDbiUtilizadaOrcamento) {
                                benfeitoria.valorCalculado = (benfeitoria.valorCustoReproducao * (100 + vm.quantificacaoCusto.valorTaxaBdi) * (100 - vm.quantificacaoCusto.valorTaxaDepreciacao));
                            } else
                                benfeitoria.valorCalculado = (benfeitoria.valorCustoReproducao * (100 - vm.quantificacaoCusto.valorTaxaDepreciacao));
                        } else {
                            if (vm.quantificacaoCusto.isTaxaDbiUtilizadaOrcamento) {
                                benfeitoria.valorCalculado = (benfeitoria.valorCustoReproducao * (100 + vm.quantificacaoCusto.valorTaxaBdi) * (100 - benfeitoria.valorTaxaDepreciacao));
                            } else
                                benfeitoria.valorCalculado = (benfeitoria.valorCustoReproducao * (100 - benfeitoria.valorTaxaDepreciacao));
                        }

                        benfeitoria.formatterValorCalculadoBenfeitoria = vm.convertNumeroPorExtenso(benfeitoria.valorCalculado);
                    });

                    vm.valorCustoTotalBenfeitoria = 0;
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        vm.valorCustoTotalBenfeitoria = vm.valorCustoTotalBenfeitoria + benfeitoria.valorCalculado;
                    });

                    vm.formatterValorCustoTotalBenfeitoria = vm.convertNumeroPorExtenso(vm.valorCustoTotalBenfeitoria);
                };

                vm.calcularEnquadramentoGrauFundamentacao = function () {
                    vm.enquadramentoGrau1 = false;
                    vm.enquadramentoGrau2 = false;
                    vm.enquadramentoGrau3 = false;

                    if (vm.identificacao.quantificacaoCustoBenfeitoria && vm.quantificacaoCusto && vm.quantificacaoCusto.tipoBdi && vm.quantificacaoCusto.tipoDepreciacao) {
                        if (vm.identificacao.quantificacaoCustoBenfeitoria === 'ORCAMENTO' && vm.quantificacaoCusto.tipoBdi !== 'ARBITRADO' && vm.quantificacaoCusto.tipoDepreciacao !== 'ARBITRADO') {
                            vm.enquadramentoGrau3 = true;
                        } else if (vm.identificacao.quantificacaoCustoBenfeitoria !== 'CUB_PROJETO_DIFERENTE' && vm.quantificacaoCusto.tipoBdi !== 'ARBITRADO') {
                            vm.enquadramentoGrau2 = true;
                        } else {
                            vm.enquadramentoGrau1 = true;
                        }
                    }
                };

                vm.calcularValorEstimado = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        if (verificaIdadeAparente(benfeitoria.idadeAparente, benfeitoria.vidaUtil) && benfeitoria.estadoConservacao) {
                            if (benfeitoria.idadeAparente !== 0) {
                                var porcentagemVida = (benfeitoria.idadeAparente * 100) / benfeitoria.vidaUtil;
                                porcentagemVida = Math.round(porcentagemVida * 100) / 100;

                                porcentagemVida = arredondaNumero(porcentagemVida);

                                var valorSubtracao = benfeitoria.estadoConservacao.id === 9 ? 2 : 1;
                                benfeitoria.valorTaxaDepreciacao = vm.tabelaDepreciacao['coluna' + porcentagemVida][benfeitoria.estadoConservacao.id - valorSubtracao];
                            }
                        }
                    });
                };

                function arredondaNumero(porcentagemVida) {
                    var valor = 2 * Math.round(porcentagemVida / 2);

                    if (valor < 2) {
                        return 2;
                    } else if (valor > 100) {
                        return 100;
                    } else if (valor % 2 !== 0) {
                        return valor + 1;
                    } else {
                        return valor;
                    }

                }

                var pesquisarEstadosConservacao = function (callback) {
                    cubAvaliacaoService.buscarEstadosConservacao().then(function (resp) {
                        vm.listaEstadosConservacao = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                function verificaIdadeAparente(idadeAparente, vidaUtil) {
                    if (idadeAparente > vidaUtil) {
                        vidaUtil = '';
                        idadeAparente = '';
                        ComumFactory.mostrarMensagemError('A idade aparente do imóvel não pode ser maior que a vida útil.');
                        return false;
                    } else if (vidaUtil === 0) {
                        vidaUtil = '';
                        ComumFactory.mostrarMensagemError('A Vida Útil não pode ser igual a 0');
                        return false;
                    }
                    return true;
                };

                vm.limparDadosBenfeitoriaDepreciacao = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        benfeitoria.vidaUtil = null;
                        benfeitoria.idadeAparente = null;
                        benfeitoria.estadoConservacao = null;
                        benfeitoria.valorTaxaDepreciacao = null;
                    });
                };

                vm.showDialogDepreciacaoHeidecke = function (ev) {
                    $mdDialog.show({
                        controller: function () {

                            vm.downloadPlanilha = function () {
                                window.open('assets/docs/Tab_Ross.pdf', "_blank");
                            }

                            return vm;
                        },
                        controllerAs: 'vm',
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDepreciacaoHeidecke.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: true
                    });
                };

                var preencheTiposCub = function (callback) {
                    vm.tiposCub = [];
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Baixo)', 1, 1, 'BAIXO', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Normal)', 1, 2, 'NORMAL', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Alto)', 1, 3, 'ALTO', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Baixo)', 4, 1, 'BAIXO', 'PP4_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Normal)', 4, 2, 'NORMAL', 'PP4_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Baixo)', 2, 1, 'BAIXO', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Normal)', 2, 2, 'NORMAL', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Alto)', 2, 3, 'ALTO', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Normal)', 3, 2, 'NORMAL', 'R16_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Alto)', 3, 3, 'ALTO', 'R16_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PIS, Padrão Residencial, Projeto de interesse social (Baixo)', 10, 1, 'BAIXO', 'PIS_PROJETO_INTERESSE_SOCIAL'));
                    vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Normal)', 5, 2, 'NORMAL', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
                    vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Alto)', 5, 3, 'ALTO', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
                    vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Normal)', 6, 2, 'NORMAL', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Alto)', 6, 3, 'ALTO', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Normal)', 7, 2, 'NORMAL', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Alto)', 7, 3, 'ALTO', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('GI, Padrão Galpão Indústrial, 1 pavimento', 8, 1, 'BAIXO', 'GI_PADRAO_GALPAO_INDUSTRIAL'));
                    vm.tiposCub.push(new TipoCub('RP1Q, Padrão Residencia Popular, 1 pavimento', 9, 1, 'BAIXO', 'RP1Q_PADRAO_RESIDENCIAL_POPULAR_QUARTO'));
                    vm.tiposCub.push(new TipoCub('CUB Médio', null, null, 'CUB_MEDIO', 'CUB_MEDIO'));

                    function TipoCub(nome, tipo, padrao, padraoAcabamento, tipoProjetoCub) {
                        this.nome = nome;
                        this.tipo = tipo;
                        this.padrao = padrao;
                        this.padraoAcabamento = padraoAcabamento;
                        this.tipoProjetoCub = tipoProjetoCub;
                    }

                    if (callback) {
                        callback();
                    }
                };

                function populaTabelaDepreciacao() {
                    vm.tabelaDepreciacao = {
                        coluna2: [1.02, 1.05, 3.51, 9.03, 18.9, 33.9, 53.1, 75.4],
                        coluna4: [2.08, 2.11, 4.55, 10, 19.8, 34.6, 53.6, 75.7],
                        coluna6: [3.18, 3.21, 5.62, 11, 20.7, 35.3, 54.1, 76],
                        coluna8: [4.32, 4.35, 6.73, 12.1, 21.6, 36.1, 54.6, 76.3],
                        coluna10: [5.5, 5.53, 7.88, 13.2, 22.6, 36.9, 55.2, 76.6],
                        coluna12: [6.72, 6.75, 9.07, 14.3, 23.6, 37.7, 55.8, 76.9],
                        coluna14: [7.98, 8.01, 10.3, 15.4, 24.6, 38.5, 56.4, 77.2],
                        coluna16: [9.28, 9.31, 11.6, 16.6, 25.7, 39.4, 57, 77.5],
                        coluna18: [10.6, 10.6, 12.9, 17.8, 26.8, 40.3, 57.6, 77.8],
                        coluna20: [12, 12, 14.2, 19.1, 27.9, 42.2, 58.3, 78.2],
                        coluna22: [13.4, 13.4, 15.6, 20.4, 29.1, 42.2, 59, 78.5],
                        coluna24: [14.9, 14.9, 17, 21.8, 30.3, 43.1, 59.6, 78.9],
                        coluna26: [16.4, 16.4, 18.5, 23.1, 31.5, 44.1, 60.4, 79.3],
                        coluna28: [17.9, 17, 20, 24.6, 32.8, 45.2, 61.1, 79.6],
                        coluna30: [19.5, 19.5, 21.5, 26, 34.1, 46.2, 61.8, 80],
                        coluna32: [21.1, 21.1, 23.1, 27.5, 35.4, 47.3, 62.6, 80.4],
                        coluna34: [22.8, 22.8, 24.7, 29, 36.8, 48.4, 63.4, 80.8],
                        coluna36: [24.5, 24.5, 26.4, 30.5, 38.1, 49.5, 64.2, 81.3],
                        coluna38: [26.2, 26.2, 28.1, 32.2, 39.6, 50.7, 65, 81.7],
                        coluna40: [28.8, 28.8, 29.9, 33.8, 41, 51.9, 65.9, 82.1],
                        coluna42: [29.8, 29.8, 31.6, 35.5, 42.5, 53.1, 66.7, 82.6],
                        coluna44: [31.7, 31.7, 33.4, 37.2, 44, 54.4, 67.6, 83.1],
                        coluna46: [33.6, 33.6, 35.2, 38.9, 45.6, 55.6, 68.5, 83.5],
                        coluna48: [35.5, 35.5, 37.1, 40.7, 47.2, 56.9, 69.4, 84],
                        coluna50: [37.5, 37.5, 39.1, 42.6, 48.8, 58.2, 70.4, 84.5],
                        coluna52: [39.5, 39.5, 41.9, 44, 50.5, 59.6, 71.3, 85],
                        coluna54: [41.6, 41.6, 43, 46.3, 52.1, 61, 72.3, 85.5],
                        coluna56: [43.7, 43.7, 45.1, 48.2, 53.9, 62.4, 63.3, 86],
                        coluna58: [45.8, 45.8, 47.2, 50.2, 55.6, 63.8, 74.3, 86.6],
                        coluna60: [48.8, 48.8, 49.3, 52.2, 57.4, 65.3, 75.3, 87.1],
                        coluna62: [50.2, 50.2, 51.5, 54.2, 59.2, 66.7, 75.4, 87.7],
                        coluna64: [52.5, 52.5, 53.7, 56.3, 61.1, 61.3, 77.5, 88.2],
                        coluna66: [54.8, 54.8, 55.9, 58.4, 69, 69.8, 78.6, 88.8],
                        coluna68: [57.1, 57.1, 58.2, 60.6, 64.9, 71.4, 79.7, 89.4],
                        coluna70: [59.5, 59.5, 60.5, 62.8, 66.8, 72.9, 80.8, 90.8],
                        coluna72: [62.2, 61.9, 62.9, 85, 68.8, 74.6, 81.9, 90.6],
                        coluna74: [64.4, 64.4, 65.3, 67.3, 70.8, 76.2, 83.1, 91.2],
                        coluna76: [66.9, 66.9, 67.7, 69.6, 72.9, 77.9, 84.3, 91.8],
                        coluna78: [69.4, 69.4, 72.7, 71.9, 74.9, 89.6, 85.5, 92.4],
                        coluna80: [72, 72, 72.7, 74.3, 77.1, 81.3, 86.7, 93.1],
                        coluna82: [74.6, 74.6, 75.3, 76.7, 79.2, 83, 88, 93.7],
                        coluna84: [77.3, 77.3, 77.8, 79.1, 81.4, 84.8, 89.2, 94.4],
                        coluna86: [80, 80, 80.5, 81.6, 83.6, 86.6, 90.5, 95],
                        coluna88: [82.7, 82.7, 83.2, 84.1, 85.8, 88.5, 91.8, 95.7],
                        coluna90: [85.5, 85.5, 85.9, 86.7, 88.1, 90.3, 93.1, 96.4],
                        coluna92: [88.3, 83.3, 88.6, 89.3, 90.4, 92.7, 94.5, 97.1],
                        coluna94: [91.2, 91.2, 91.4, 91.9, 92.8, 94.1, 95.8, 97.8],
                        coluna96: [94.1, 94.1, 94.2, 94.6, 95.1, 96, 97.2, 98.5],
                        coluna98: [97, 97, 97.1, 97.3, 97.6, 98, 98, 99.8],
                        coluna100: [100, 100, 100, 100, 100, 100, 100, 100]
                    };
                };

                vm.cancelModal = function () {
                    $mdDialog.cancel();
                };

                init();
            }
        };
    };

})
();
(function () {

    directive.$inject = ["identificacaoLaudoRelatorioService", "$state", "$filter", "avaliacaoEscopoCompartilhadoService", "usuarioAvaliacaoService", "documentoSeiService", "ComumFactory", "$mdDialog"];
    angular.module('su-avaliacao')
        .directive('identificacaoLaudo', directive);

    function directive(identificacaoLaudoRelatorioService, $state, $filter, avaliacaoEscopoCompartilhadoService, usuarioAvaliacaoService, documentoSeiService, ComumFactory, $mdDialog) {
        return {
            restrict: 'EA',
            scope: {
                identificacao: '=',
                ripImovel: '=',
                formularioValidacao: '=',
                listaResponsaveisTecnicos: '=',
                listaNormasUtilizadas: '=',
                benfeitorias: '=',
                imovel: '=',
                buscarDadosImovelPorRip: '=',
                detalharLaudoRelatorio: '=',
                limparDadosListaBenfeitorias: '=',
                limparDadosQuantificacaoCusto: '=',
                isRipBloqueado: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/identificacao.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.tiposIdentificacao = [];
                vm.metodosAvaliacao = [];
                vm.tratamentosIdentificacao = [];
                vm.ferramentasAnaliticas = [];

                vm.LAUDO = 'LAUDO';
                vm.LAUDO_LOCACAO_ARRENDAMENTO = 'LAUDO_LOCACAO_ARRENDAMENTO';
                vm.COMPARATIVO_MERCADO = 'COMPARATIVO_MERCADO';
                vm.EVOLUTIVO = 'EVOLUTIVO';
                vm.responsavel = {};
                vm.nomeCampo = 'Identificação';

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;
                vm.paginationNormas = [];

                vm.editar = {};
                vm.editar.buttonShow = false;
                vm.normativos = {};
                vm.descricao = {};

                var init = function () {
                    buscarTiposIdentificacao();
                    buscarMetodosAvaliacao();
                    buscarTratamentosIdentificacao();
                    buscarFerramentasAnaliticas();
                    buscarMetodologia();
                    buscarTiposAvaliacaoTerreno();
                    buscarQuantificacaoCustoBenfeitorias();
                    vm.verificarAvaliacoesExistentes();


                    if (!vm.listaNormasUtilizadas) {
                        vm.listaNormasUtilizadas = [];
                    }
                    vm.paginacaoNormas();
                    buscarTiposNorma();

                };

                var buscarTiposIdentificacao = function () {
                    identificacaoLaudoRelatorioService.buscarTiposIdentificacao().then(function (resp) {
                        vm.tiposIdentificacao = resp.data;
                    });
                };

                var buscarMetodosAvaliacao = function (callback) {
                    identificacaoLaudoRelatorioService.buscarMetodosAvaliacao().then(function (resp) {
                        vm.metodosAvaliacao = resp.data;
                        if (callback) {
                            callback();
                        }

                    });
                };

                var buscarMetodologia = function () {
                    identificacaoLaudoRelatorioService.buscarMetodologia().then(function (resp) {
                        vm.listaMetodologia = resp.data;
                    });
                };

                var buscarTratamentosIdentificacao = function () {
                    identificacaoLaudoRelatorioService.buscarTratamentosIdentificacao().then(function (resp) {
                        vm.tratamentosIdentificacao = resp.data;
                    });
                };

                var buscarFerramentasAnaliticas = function () {
                    identificacaoLaudoRelatorioService.buscarFerramentasAnaliticas().then(function (resp) {
                        vm.ferramentasAnaliticas = resp.data;
                    });
                };

                var buscarQuantificacaoCustoBenfeitorias = function (callback) {
                    identificacaoLaudoRelatorioService.buscarQuantificacaoCustoBenfeitorias().then(function (resp) {
                        vm.listaQuantificacaoCustoBenfeitorias = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                var buscarTiposAvaliacaoTerreno = function () {
                    identificacaoLaudoRelatorioService.buscarTiposAvaliacaoTerreno().then(function (resp) {
                        vm.tiposAvaliacaoTerreno = resp.data;
                    });
                };

                vm.consultarImovelPeloRipInformado = function () {
                    if (vm.identificacao.rip && vm.identificacao.rip !== '') {
                        vm.identificacao.rip = ("00000000" + vm.identificacao.rip).slice(-8);
                        vm.buscarDadosImovelPorRip(vm.identificacao.rip);
                    }

                    vm.verificarAvaliacoesExistentes();
                };

                vm.redirecionarConsultarImovelLaudo = function () {
                    vm.tela = 'CADASTRAR_LAUDO_RELATORIO_AVALIACAO';
                    avaliacaoEscopoCompartilhadoService.setObjetos('tela', vm.tela);
                    $state.go('avaliacao.CONSULTAR_IMOVEL_LAUDO');
                };

                vm.changeTipoIdentificacao = function () {
                    vm.identificacao.metodoAvaliacao = null;
                    vm.identificacao.tratamento = null;
                    vm.identificacao.ferramentaAnalitica = null;
                    vm.identificacao.tipoAvaliacaoTerreno = null;
                    vm.identificacao.quantificacaoCustoBenfeitoria = null;
                    vm.identificacao.metodologia = null;
                    vm.identificacao.metodologiaOutros = null;

                    vm.verificarAvaliacoesExistentes();
                };

                vm.changeMetodologiaRelatorio = function () {
                    vm.identificacao.metodologiaOutros = null;

                    if (vm.identificacao.metodologia === 'GLEBA_URBANIZAVEL' && vm.imovel.tipoImovel.descricao !== 'Gleba/Terreno/Lote sem edificação') {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-tipo-relatorio-imoveis-sem-edificacao'));
                        vm.identificacao.metodologia = null;
                    }
                };

                vm.changeMetodoAvaliacao = function () {

                    if ((vm.identificacao.tipo === 'LAUDO' || vm.identificacao.tipo === 'LAUDO_LOCACAO_ARRENDAMENTO') && vm.identificacao.metodoAvaliacao === 'EVOLUTIVO') {
                        if (vm.benfeitorias.benfeitoriasAvaliacao && vm.benfeitorias.benfeitoriasAvaliacao.length > 0) {
                            var edificacaoTrue = false;
                            for (var i = 0; i < vm.benfeitorias.benfeitoriasAvaliacao.length; i++) {
                                if (vm.benfeitorias.benfeitoriasAvaliacao[i].benfeitoriaImovel.tipoBenfeitoria.descricao === 'Edificação') {
                                    edificacaoTrue = true;
                                }
                            }
                            if (!edificacaoTrue) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-metodo-avaliacao-invalido'));
                                vm.identificacao.metodoAvaliacao = null;
                                return;
                            } else {
                                vm.identificacao.tipoAvaliacaoTerreno = 'COMPARATIVO';
                                vm.identificacao.tratamento = 'CIENTIFICO';
                                vm.identificacao.ferramentaAnalitica = 'REGRESSAO_LINEAR';
                                return;
                            }
                        }
                        if (vm.imovel.benfeitorias) {
                            var edificacaoTrue = false;
                            for (var i = 0; i < vm.imovel.benfeitorias.length; i++) {
                                if (vm.imovel.benfeitorias[i].tipoBenfeitoria.descricao === 'Edificação') {
                                    edificacaoTrue = true;
                                }
                            }
                            if (!edificacaoTrue) {
                                ComumFactory.mostrarMensagemError($filter('translate')('mensagem-metodo-avaliacao-invalido'));
                                vm.identificacao.metodoAvaliacao = null;
                                return;
                            } else {
                                vm.identificacao.tipoAvaliacaoTerreno = 'COMPARATIVO';
                                vm.identificacao.tratamento = 'CIENTIFICO';
                                vm.identificacao.ferramentaAnalitica = 'REGRESSAO_LINEAR';
                                return;
                            }
                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-metodo-avaliacao-invalido'));
                            vm.identificacao.metodoAvaliacao = null;
                            return;
                        }
                    }

                    vm.identificacao.tratamento = null;
                    vm.identificacao.ferramentaAnalitica = null;
                    vm.identificacao.tipoAvaliacaoTerreno = null;
                    vm.identificacao.quantificacaoCustoBenfeitoria = null;


                };

                vm.verificarAvaliacoesExistentes = function () {
                    if (vm.identificacao.rip && vm.identificacao.tipo) {
                        identificacaoLaudoRelatorioService.verificarAvaliacoesExistentes(vm.identificacao.rip, vm.identificacao.tipo).then(function (resp) {
                            vm.aviso = resp.data;
                        });
                    }
                };

                var buscarTiposNorma = function () {
                    identificacaoLaudoRelatorioService.buscarTiposNorma().then(function (resp) {
                        vm.listaTiposNorma = $filter('orderBy')(resp.data, 'nome', false);
                    });
                };

                vm.adicionarNorma = function () {
                    if (vm.normativos.tipo) {
                        if (vm.normativos.tipo === 'OUTROS' && !vm.descricao.outros) {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                            return
                        }

                        if (!verificarJaExiste(vm.normativos.id)) {
                            vm.listaNormasUtilizadas.push({
                                norma: {
                                    id: vm.normativos.id,
                                    tipo: vm.normativos.tipo,
                                    nome: vm.normativos.nome
                                },
                                descricao: vm.descricao.outros
                            });

                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-norma-existe'));
                        }
                        vm.normativos = {};
                        vm.paginacaoNormas();
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                    }
                };

                vm.alterarNorma = function ($index) {
                    vm.normativos = angular.copy(vm.paginationNormas[$index]);
                    vm.descricao.outros = vm.identificacao.descricaoOutros;
                    vm.editar.index = $index;
                    vm.editar.buttonShow = true;
                };

                vm.gravarAlteracaoNorma = function () {
                    if (vm.normativos.tipo != 'OUTROS' || (vm.normativos.tipo === 'OUTROS' && vm.descricao.outros)) {
                        vm.listaNormasUtilizadas.splice(vm.editar.index, 1);
                        if (!verificarJaExiste(vm.normativos.id)) {
                            vm.listaNormasUtilizadas.push({
                                norma: {
                                    id: vm.normativos.id,
                                    tipo: vm.normativos.tipo,
                                    nome: vm.normativos.nome
                                },
                                descricao: vm.descricao.outros
                            });

                        } else {
                            ComumFactory.mostrarMensagemError($filter('translate')('mensagem-norma-existe'));
                        }
                        vm.normativos = {};
                        vm.paginacaoNormas();
                        vm.editar.index = null;
                        vm.editar.buttonShow = false;
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                    }
                };

                var verificarJaExiste = function (id) {
                    var idExiste = false;
                    angular.forEach(vm.listaNormasUtilizadas, function (item) {
                        if (item.norma.id == id && item.norma.tipo != 'OUTROS') {
                            idExiste = true;
                        }
                    });
                    return idExiste;
                };

                vm.removerNorma = function ($index) {
                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent($filter('translate')('mensagem-remover-responsavel-tecnico-confirm'))
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        vm.listaNormasUtilizadas.splice($index, 1);
                        vm.identificacao.descricaoOutros = null;
                        vm.paginacaoNormas();
                    });
                };

                vm.paginacaoNormas = function () {
                    vm.paginationNormas = [];
                    vm.totalItems = vm.listaNormasUtilizadas.length;
                    if (vm.totalItems < vm.tamanhoLimite) {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationNormas.push(vm.listaNormasUtilizadas[i]);
                        }

                    } else {
                        for (var i = ((vm.registroInicial - 1) * vm.tamanhoLimite); i < vm.totalItems; i++) {
                            vm.paginationNormas.push(vm.listaNormasUtilizadas[i]);
                            if (vm.paginationNormas.length == vm.tamanhoLimite) {
                                break;
                            }
                        }
                    }
                };

                vm.$watch('identificacao', function () {
                    vm.paginacaoNormas();

                    buscarListaOrgaos(function () {
                        vm.buscarListaUnidades();
                    });
                });

                var buscarListaOrgaos = function (callback) {
                    usuarioAvaliacaoService.getOrgaos().then(function (resp) {
                        vm.listaOrgaos = resp.data;
                        if (vm.listaOrgaos.length === 1) {
                            vm.identificacao.orgao = vm.listaOrgaos[0];
                            vm.buscarListaUnidades();
                        }
                        if (callback) {
                            callback();
                        }
                    });
                };

                vm.buscarListaUnidades = function () {
                    if (vm.identificacao.orgao) {
                        usuarioAvaliacaoService.getUnidades(vm.identificacao.orgao).then(function (resp) {
                            vm.listaUnidades = resp.data;
                            if (vm.listaUnidades.length === 1) {
                                vm.identificacao.unidade = vm.listaUnidades[0];
                            }
                        });
                    }
                };

                vm.changeLaudoAvaliacaoDocumentoSei = function () {
                    vm.identificacao.laudoRelatorioDocumentoSei.tipoEnviarDocumento = null;
                    vm.identificacao.laudoRelatorioDocumentoSei.numeroProcessoSei = null;
                };

                vm.changeLaudoAvaliacaoNumeroSei = function () {
                    vm.identificacao.laudoRelatorioDocumentoSei.numeroProcessoSei = null;
                };

                vm.validarLaudoAvaliacaoNumeroSeiProcessoSei = function () {
                    documentoSeiService.recuperarProcedimentoSei(vm.identificacao.laudoRelatorioDocumentoSei.numeroProcessoSei, vm.imovel.enderecamento.uf).then(function () {

                    }, function (erro) {
                        vm.identificacao.laudoRelatorioDocumentoSei.numeroProcessoSei = null;
                        ComumFactory.mostrarMensagemError(erro.data.message);
                    });
                };


                init();
            }
        };
    }

})();

(function () {

    directive.$inject = ["dominioAvaliacaoService"];
    angular.module('su-avaliacao')
        .directive('detalhesImovelLaudo', directive);

    function directive (dominioAvaliacaoService) {
        return {
            restrict: 'EA',
            scope: {
                rip: '=',
                imovel: '=',
                formularioValidacao: '=',
                detalhesImovel: '=',
                listaMatriculasTranscricao: '=',
                listaCertidoesCartoriais: '=',
                dadosEnderecamento: '=',
                dadosProprietario: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/detalhesImovel.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.BRASIL = 'Brasil';
                vm.listaTiposImovel = [];
                vm.listaNaturezasImovel = [];
                vm.listaTiposProprietarios = [];
                vm.listaTiposProprietarioParcial = [];

                vm.isObservacoesGerais = true;

                vm.oldObservacoesGerais = null;

                vm.UNIAO = 'União';
                vm.AUTARQUIA_FUNDACAO = 'Autarquia/Fundação';
                vm.AUTARQUIA_FUNDACAO_FEDERAL = 'Autarquia/Fundação Federal';
                vm.TERCEIROS = 'Terceiros';


                var init = function() {

                    buscarTiposImovel();
                    buscarNaturezasImovel();
                    buscarTiposProprietarioReal();
                    buscarTiposProprietariosParcial();
                };

                var buscarTiposImovel = function() {
                    dominioAvaliacaoService.consultarTiposImovelSU().then(function(resp) {
                        vm.listaTiposImovel = resp.data;
                    });
                };

                var buscarNaturezasImovel = function() {
                    dominioAvaliacaoService.consultarNaturezasImovelSU().then(function(resp) {
                        vm.listaNaturezasImovel = resp.data;
                    });
                };

                var buscarTiposProprietarioReal = function() {
                    dominioAvaliacaoService.pesquisarTiposProprietarioRealIncorporacao()
                            .then(function(resp) {
                        vm.listaTiposProprietarios = resp.data;
                    });
                };

                var buscarTiposProprietariosParcial = function() {
                    dominioAvaliacaoService.pesquisarTiposProprietarioParcialIncorporacao()
                            .then(function(resp) {
                        vm.listaTiposProprietarioParcial = resp.data;
                    });
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["arquivoAvaliacaoService", "laudoAvaliacaoService"];
    angular.module('su-avaliacao')
        .directive('detalharRevalidacao', directive);

    function directive(arquivoAvaliacaoService, laudoAvaliacaoService) {
        return {
            restrict: 'EA',
            scope: {
                laudoAvaliacao: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/detalharRevalidacao.html',
            link: function ($scope) {

                var vm = $scope;

                var init = function () {
                    vm.revalidacoes = vm.laudoAvaliacao.revalidacoes.filter(function (revalidacao) {
                        return revalidacao.dataAssinatura !== null || revalidacao.situacao == 'PENDENTE_VALIDACAO_CHEFIA';
                    });
                };

                vm.listaSituacoes = {
                        TODOS: "Todos",
                        VIGENTE_REVALIDADO: "Vigente/Revalidado",
                        VIGENTE: "Vigente",
                        ATIVO_REVALIDADO: "Válido/Revalidado",
                        VALIDO: "Válido",
                        REVALIDACAO_PREENCHIMENTO: "Revalidação em Preenchimento",
                        PENDENTE_VALIDACAO_CHEFIA: "Pendente de Validação da Chefia",
                        NAO_ACEITO_CHEFIA: "Não Aceito Pela Chefia",
                        CANCELADO: "Cancelado",
                        EM_PREENCHIMENTO: "Cadastro em Preenchimento"
                    };

                vm.verificarExibicaoDownloadLaudoRevalidacao = function (situacao) {
                    var situacoesNegadas = [
                        'PENDENTE_VALIDACAO_CHEFIA',
                        'REVALIDACAO_PREENCHIMENTO'
                    ];

                    return !situacoesNegadas.includes(situacao);
                };

                vm.verificarExibicaoDocumentoRevalidacaoLaudo = function(situacao) {
                    var situacoesPermitidas = [
                        'PENDENTE_VALIDACAO_CHEFIA',
                        'REVALIDACAO_PREENCHIMENTO'
                    ];

                    return situacoesPermitidas.includes(situacao);
                };

                vm.situacoesPendentes = function (situacao) {
                    var situacoesPendentes = [
                        'PENDENTE_VALIDACAO_CHEFIA',
                        'REVALIDACAO_PREENCHIMENTO',
                        'NAO_ACEITO_CHEFIA'
                    ];

                    return situacoesPendentes.includes(situacao);
                };

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                vm.downloadRevalidacaoLaudoAvaliacao = function () {
                    return laudoAvaliacaoService.downloadRevalidacaoLaudoAvaliacao(vm.idLaudo);
                };

                init();
            }
        };
    }

})();


(function () {

    angular.module('su-avaliacao')
        .directive('cancelamentoLaudo', directive);

    function directive () {
        return {
            restrict: 'EA',
            scope: {
                detalharLaudoRelatorio: '='

            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/cancelamentoLaudo.tmpl.html',
            link: function ($scope) {

                var vm = $scope;
                vm.detalharLaudoRelatorio = false;


            }
        };
    };

})();
(function () {

    directive.$inject = ["benfeitoriasLaudoRelatorioService"];
    angular.module('su-avaliacao')
        .directive('benfeitoriasLaudo', directive);

    function directive(benfeitoriasLaudoRelatorioService) {
        return {
            restrict: 'EA',
            scope: {
                benfeitorias: '=',
                listaBenfeitoriaAvaliacao: '=',
                redirecionarCadastrarDocumentoEdificacaoBenfeitoria : '=',
                formularioValidacao: '=',
                detalharLaudoRelatorio: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/benfeitorias.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaTipoBenfeitoria = [];
                vm.listaTipoComplementar = [];
                vm.listaTipoEdificacao = [];
                vm.isTipoProjeto = true;
                vm.condicoes = [
                    {
                        nome: "Sim",
                        valor: true

                    }, {
                        nome: "Não",
                        valor: false
                    }
                ];

                var init = function () {
                    buscarTiposBenfeitoria();
                    buscarTiposComplementares();
                    buscarTiposEdificacao();
                    buscarTiposProjeto();


                };

                var buscarTiposProjeto = function () {
                    benfeitoriasLaudoRelatorioService.buscarTiposProjeto().then(function (resp) {
                        vm.listaTiposProjeto = resp.data;
                    });
                };

                var buscarTiposBenfeitoria = function () {
                    benfeitoriasLaudoRelatorioService.buscarTiposBenfeitoria().then(function (resp) {
                        vm.listaTipoBenfeitoria = resp.data;
                    });
                };

                var buscarTiposComplementares = function () {
                    benfeitoriasLaudoRelatorioService.buscarTiposEspecializacaoComplementar()
                        .then(function (resp) {
                            vm.listaTipoComplementar = resp.data;
                        });
                };

                var buscarTiposEdificacao = function () {
                    benfeitoriasLaudoRelatorioService.buscarTiposEspecializacaoEdificacao()
                        .then(function (resp) {
                            vm.listaTipoEdificacao = resp.data;
                        });
                };


                init();
            }
        };
    }

})();
(function () {

    directive.$inject = ["avaliacaoLaudoService", "numeroExtensoAvaliacaoService", "identificacaoLaudoRelatorioService", "avaliacaoEscopoCompartilhadoService", "$mdDialog", "$filter", "$window", "$state", "$timeout", "arquivoAvaliacaoService", "ComumFactory"];
    angular.module('su-avaliacao')
        .directive('avaliacaoLaudo', directive);

    function directive(avaliacaoLaudoService, numeroExtensoAvaliacaoService, identificacaoLaudoRelatorioService, avaliacaoEscopoCompartilhadoService, $mdDialog, $filter, $window, $state, $timeout, arquivoAvaliacaoService, ComumFactory) {
        return {
            restrict: 'EA',
            scope: {
                laudoRelatorioAvaliacao: '=',
                avaliacao: '=',
                identificacao: '=',
                formularioValidacao: '=',
                listaBenfeitoriaAvaliacao: '=',
                unidadeAutonomaIncorporacao: '=',
                redirecionarInformarValorCub: '=',
                redirecionarInformarValorEdificacao: '=',
                redirecionarSelecionarAmostras: '=',
                redirecionarAdicionarVariavelAmostras: '=',
                salvarComoRascunhoLaudoRelatorio: '=',
                tipoValorLaudo: '=',
                ferramentaAnalitica: '=',
                detalharLaudoRelatorio: '=',
                unidadeMedida: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/avaliacao.tmpl.html',
            link: function ($scope) {

                var vm = $scope;
                var content_type1 = ['image/pjpeg', 'image/jpeg', 'image/png'];
                vm.listaTiposValores = [];
                vm.listaNivelFundamentacao = [];
                vm.ferramentasAnaliticas = [];
                vm.VALOR_MEDIO = 'VALOR_MEDIO';
                vm.VALOR_MINIMO = 'VALOR_MINIMO';
                vm.VALOR_MAXIMO = 'VALOR_MAXIMO';
                vm.OUTRO_VALOR = 'OUTRO_VALOR';
                vm.documento = {};
                vm.dataAtual = new Date();

                vm.NAO_ATINGIDO = 'NAO_ATINGIDO';

                vm.CONTENT_TYPE_02 = 'CONTENT_TYPE_02';

                vm.LAUDO_RELATORIO = 'LAUDO_RELATORIO';
                vm.CUB = 'CUB';
                vm.OUTRA_FONTE = 'OUTRA_FONTE';
                vm.PVG_SPU = 'PVG_SPU';
                vm.PVG_MUNICIPIO = 'PVG_MUNICIPIO';
                vm.PVG_INCRA = 'PVG_INCRA';
                vm.formatterValorMedio = null;
                vm.formatterValorMinimo = null;
                vm.formatterValorMaximo = null;
                vm.formatterOutroValorAdotado = null;

                vm.variacaoValorAdotado = null;
                vm.variacaoSuperiorValorMedio = null;
                vm.variacaoInferiorValorMedio = null;
                vm.tipoValorTerrenoAvaliacao = null;

                vm.listaMoedas = [
                    {
                        id: 1,
                        codigo: '001',
                        nome: 'Real',
                        simbolo: 'R$',
                        codigoPais: '55',
                        pais: 'Brasil',
                        dataExclusao: new Date()
                    }, {
                        id: 2,
                        codigo: '002',
                        nome: 'Dolar',
                        simbolo: '$',
                        codigoPais: '00',
                        pais: 'Estados Unidos',
                        dataExclusao: new Date()
                    }
                ];

                vm.icAdocaoEstimativa = true;
                vm.valorAdotadoSelecionado = 0;
                vm.valorTotalTerreno = 0;

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                var init = function () {
                    buscarFerramentasAnaliticas();
                    buscarTiposValores();
                    vm.buscarPressupostos();

                };

                vm.$watch('avaliacao', function () {
                    if (vm.avaliacao.tipoValorLaudo === vm.CUB) {
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                            if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao != 'Complementar') {
                                vm.valorTotalTerreno += benfeitoria.benfeitoriaImovel.valor;
                            }
                        });
                    } else if (vm.avaliacao.tipoValorLaudo === vm.OUTRA_FONTE) {
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                            if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao != 'Complementar') {
                                vm.valorTotalTerreno += benfeitoria.benfeitoriaImovel.valorEdificacao.valor;
                            }
                        });
                    }

                    if (vm.avaliacao.tipoValorTerrenoAvaliacao != null) {
                        vm.changeTipoValorTerrenoAvaliacao(vm.avaliacao.tipoValorTerrenoAvaliacao);
                    }
                    if (!vm.avaliacao.anexos) {
                        vm.avaliacao.anexos = [];
                    }
                    if (vm.avaliacao.resultadosAvaliacao) {
                        vm.calcularVariacaoValorAdotado();
                    }
                    if (vm.avaliacao.resultadosAvaliacao) {
                        vm.avaliacao.resultadosAvaliacao.moeda = vm.listaMoedas[0];
                    }
                    if (!vm.avaliacao.documentosPressupostos) {
                        vm.avaliacao.documentosPressupostos = [];
                    } else {
                        for (var i = 0; i < vm.avaliacao.documentosPressupostos.length; i++) {
                            vm.selecionaTipo(vm.avaliacao.documentosPressupostos[i])
                        }
                    }
                    vm.avaliacao.valorTotalTerreno = vm.valorTotalTerreno;
                });

                vm.$watch('listaBenfeitoriaAvaliacao', function () {
                    if(vm.listaBenfeitoriaAvaliacao && vm.listaBenfeitoriaAvaliacao.length > 0) {
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                            if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao != 'Complementar') {
                                vm.isBenfeitoriaEdificacao = true;
                            }
                            if (vm.isBenfeitoriaEdificacao) {
                                vm.isBenfeitoriaSemEdificacao = false;
                            } else {
                                vm.isBenfeitoriaSemEdificacao = true;
                            }
                        });
                    }else if(vm.unidadeAutonomaIncorporacao && vm.unidadeAutonomaIncorporacao.id){
                        vm.isUnidadeAutonoma = true
                    } else{
                        vm.isBenfeitoriaSemEdificacao = true;
                    }

                });

                vm.$watch('listaBenfeitoriaAvaliacao', function () {
                    angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                        if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao != 'Complementar') {
                            vm.isBenfeitoriaEdificacao = true;
                        }
                        if(vm.isBenfeitoriaEdificacao){
                            vm.isBenfeitoriaSemEdificacao = false;
                        }else{
                            vm.isBenfeitoriaSemEdificacao = true;
                        }
                    });

                });

                var buscarTiposValores = function () {
                    avaliacaoLaudoService.ordenacaoTipoValor().then(function (resp) {
                        vm.listaTiposValores = resp.data;
                    });
                };

                var buscarFerramentasAnaliticas = function () {
                    identificacaoLaudoRelatorioService.buscarFerramentasAnaliticas().then(function (resp) {
                        vm.ferramentasAnaliticas = resp.data;
                    });
                };

                vm.alterandoMoedaValores = function () {
                    if (vm.avaliacao.resultadosAvaliacao) {
                        vm.formatterValorMedio = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMedio);
                        vm.formatterValorMinimo = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMinimo);
                        vm.formatterValorMaximo = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMaximo);
                        vm.formatterOutroValorAdotado = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.outroValorAdotado);
                        vm.calcularVariacaoValorAdotado();
                    }
                };

                vm.calcularVariacaoValorAdotado = function () {
                    if (vm.avaliacao.resultadosAvaliacao.valorMedio && vm.avaliacao.resultadosAvaliacao.tipoValor == vm.OUTRO_VALOR
                        && vm.avaliacao.resultadosAvaliacao.outroValorAdotado) {
                        vm.variacaoValorAdotado = vm.avaliacao.resultadosAvaliacao.outroValorAdotado;
                        vm.variacaoSuperiorValorMedio = (vm.avaliacao.resultadosAvaliacao.valorMedio + (vm.avaliacao.resultadosAvaliacao.valorMedio * 0.01));
                        vm.variacaoInferiorValorMedio = (vm.avaliacao.resultadosAvaliacao.valorMedio - (vm.avaliacao.resultadosAvaliacao.valorMedio * 0.01));
                    }
                    vm.verificarAdocaoEstimativaValor();
                };

                vm.verificarAdocaoEstimativaValor = function () {
                    vm.icAdocaoEstimativa = true;
                    var bool = !(vm.variacaoValorAdotado && (vm.variacaoValorAdotado < vm.variacaoInferiorValorMedio || vm.variacaoValorAdotado > vm.variacaoSuperiorValorMedio));
                    if (bool) {
                        vm.icAdocaoEstimativa = false;
                    }
                };

                vm.determinarValorAdotadoSelecionado = function () {
                    if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MEDIO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMedio;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MINIMO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMinimo;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MAXIMO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMaximo;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.OUTRO_VALOR) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.outroValorAdotado;
                    }
                };

                vm.changeTipoValorTerrenoAvaliacao = function (tipo) {

                    vm.showValorTerreno = false;
                    if (tipo != vm.OUTRA_FONTE) {
                        avaliacaoLaudoService.buscarValorTerreno(vm.identificacao.rip, tipo).then(function (resp) {
                            vm.pvg = resp.data;
                            vm.showValorTerreno = true;
                            if (vm.pvg === "") {
                                vm.showValorTerreno = false;
                                var confirm = $mdDialog.confirm({focusOnOpen: false})
                                    .textContent($filter('translate')('mensagem-incluir-fonte-valor-terreno'))
                                    .ok($filter('translate')('btn-sim'))
                                    .cancel($filter('translate')('btn-nao'));
                                $mdDialog.show(confirm).then(function () {
                                    var url = $state.href('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                        ripDetalhar: vm.identificacao.rip
                                      });                                       
                                  $window.open(url, '_blank');                                                                 
                                }, function () {
                                    $mdDialog.cancel();
                                });
                            }
                        });
                    }
                };

                vm.changeTipoValorLaudo = function () {
                    vm.valorTotalTerreno = 0;
                    if (vm.listaBenfeitoriaAvaliacao && vm.listaBenfeitoriaAvaliacao.length > 0) {
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                            benfeitoria.benfeitoriaImovel.valor = null;
                            benfeitoria.benfeitoriaImovel.padraoAcabamento = null;
                            benfeitoria.benfeitoriaImovel.tipoProjetoCub = null;
                            benfeitoria.benfeitoriaImovel.idadeAparente = null;
                            benfeitoria.benfeitoriaImovel.vidaUtil = null;
                            benfeitoria.benfeitoriaImovel.estadoConservacao = null;
                            benfeitoria.benfeitoriaImovel.valorCub = null;
                            benfeitoria.benfeitoriaImovel.valorCubDepreciado = null;
                            benfeitoria.benfeitoriaImovel.valorEdificacao = null;
                        });
                    } else if (vm.unidadeAutonomaIncorporacao) {
                        vm.unidadeAutonomaIncorporacao.valor = null;
                        vm.unidadeAutonomaIncorporacao.padraoAcabamento = null;
                        vm.unidadeAutonomaIncorporacao.tipoProjetoCub = null;
                    }
                };

                vm.convertNumeroPorExtenso = function (numero) {
                    vm.valorPorExtenso = numeroExtensoAvaliacaoService.converterNumeroPorExtenso(numero, "real");
                };

                /** Seleciona o Documento */
                vm.adicionarArquivoDocumento = function (file) {

                    console.log('file1');
                    console.log(file);

                    if ((content_type1.indexOf(file[0].type) != -1)) {
                        arquivoAvaliacaoService.uploadArquivo(file[0]).then(function (resp) {
                            if (!vm.documento) {
                                vm.documento = {};
                            }

                            vm.documento.arquivo = resp.data;
                        });
                    } else {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-arquivo-formato-invalido'));
                    }
                };
                /** Adiciona à lista de Documentos */
                vm.adicionarListaDocumentos = function () {

                    vm.formEnviado = true;

                    if (!vm.documento || !vm.documento.arquivo || !vm.documento.descricao || !vm.documento.dtArquivo || !vm.documento.pressuposto) {
                        ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                        return;
                    }

                    if (!vm.avaliacao.documentosPressupostos) {
                        vm.avaliacao.documentosPressupostos = [];
                    }

                    vm.avaliacao.documentosPressupostos.push(vm.documento);
                    vm.formEnviado = false;
                    for (var i = 0; i < vm.avaliacao.documentosPressupostos.length; i++) {
                        vm.selecionaTipo(vm.avaliacao.documentosPressupostos[i])
                    }

                    vm.documento = {};
                };

                vm.removerDocumento = function (documento) {

                    var confirm = $mdDialog.confirm()
                        .title("Atenção!")
                        .textContent("Deseja remover este arquivo?")
                        .ok("Confirmar")
                        .cancel("Cancelar");

                    $mdDialog.show(confirm).then(function () {
                        arquivoAvaliacaoService.deletarArquivo(documento.arquivo.id).then(function () {
                            var existe = false;

                            for (var i = 0; i < vm.avaliacao.documentosPressupostos.length; i++) {
                                if (vm.avaliacao.documentosPressupostos[i].$edit) {
                                    vm.avaliacao.documentosPressupostos.splice(i, 1);
                                }

                            }
                            if (vm.avaliacao.documentosPressupostos && vm.avaliacao.documentosPressupostos.length > 0) {
                                for (var i = 0; i < vm.avaliacao.documentosPressupostos.length; i++) {
                                    if (documento.pressuposto === vm.avaliacao.documentosPressupostos[i].pressuposto) {
                                        existe = true;
                                        break
                                    }
                                }
                            } else {
                                vm.mostrarColinearidade = false;
                                vm.mostrarHomocedasticidade = false;
                                vm.mostrarLinearidade = false;
                                vm.mostrarNormalidade = false;
                                vm.mostrarPontos = false;
                                vm.mostrarVerificacao = false;
                            }

                            if (!existe) {
                                switch (documento.pressuposto) {

                                    case 'COLINEARIDADE_OU_MULTICOLINEARIDADE':
                                        vm.mostrarColinearidade = false;
                                        break;

                                    case 'HOMOCEDASTICIDADE':
                                        vm.mostrarHomocedasticidade = false;
                                        break;

                                    case 'LINEARIDADE':
                                        vm.mostrarLinearidade = false;
                                        break;

                                    case 'NORMALIDADE':
                                        vm.mostrarNormalidade = false;
                                        break;

                                    case 'PONTOS_INFLUENCIANTES_OU_OUTLIERS':
                                        vm.mostrarPontos = false;
                                        break;

                                    case 'VERIFICACAO_DE_AUTOCORRELACAO':
                                        vm.mostrarVerificacao = false;
                                        break;
                                }
                            }
                        });
                    });
                };

                vm.selecionaTipo = function (documento) {

                    switch (documento.pressuposto) {

                        case 'COLINEARIDADE_OU_MULTICOLINEARIDADE':
                            vm.mostrarColinearidade = true;
                            break;

                        case 'HOMOCEDASTICIDADE':
                            vm.mostrarHomocedasticidade = true;
                            break;

                        case 'LINEARIDADE':
                            vm.mostrarLinearidade = true;
                            break;

                        case 'NORMALIDADE':
                            vm.mostrarNormalidade = true;
                            break;

                        case 'PONTOS_INFLUENCIANTES_OU_OUTLIERS':
                            vm.mostrarPontos = true;
                            break;

                        case 'VERIFICACAO_DE_AUTOCORRELACAO':
                            vm.mostrarVerificacao = true;
                            break;
                    }
                };

                vm.fileLimitValido = function () {
                    return !vm.fileLimit || (vm.fileLimit > (vm.avaliacao.documentosPressupostos ? vm.avaliacao.documentosPressupostos.length : 0));
                };

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                vm.buscarPressupostos = function () {
                    avaliacaoLaudoService.buscarPressupostos().then(function (resp) {
                        vm.listaPressupostos = resp.data;
                    })
                };

                init();
            }
        };
    };

})();

(function () {

    directive.$inject = ["avaliacaoLaudoService", "numeroExtensoAvaliacaoService", "$mdDialog", "$filter", "$state", "$timeout", "dominioPlenoService", "ComumFactory"];
    angular.module('su-avaliacao')
        .directive('avaliacaoGlebaUrbanizavel', directive);

    function directive(avaliacaoLaudoService, numeroExtensoAvaliacaoService, $mdDialog, $filter, $state, $timeout, dominioPlenoService, ComumFactory) {
        return {
            restrict: 'EA',
            scope: {
                laudoRelatorioAvaliacao: '=',
                avaliacao: '=',
                quantificacaoCusto: '=',
                identificacao: '=',
                formularioValidacao: '=',
                listaBenfeitoriaAvaliacao: '=',
                unidadeAutonomaIncorporacao: '=',
                redirecionarInformarValorEdificacao: '=',
                redirecionarSelecionarAmostras: '=',
                redirecionarAdicionarVariavelAmostras: '=',
                salvarComoRascunhoLaudoRelatorio: '=',
                salvarComoRascunhoLaudoRelatorioCadastrarAvaliacao: '=',
                tipoValorLaudo: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '=',
                uf: '=',
                areaTotalTerreno: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/avaliacaoGlebaUrbanizavel.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.CONTENT_TYPE_02 = 'CONTENT_TYPE_02';
                vm.dataAtual = new Date();

                vm.listaModelos = {
                    HELIO_CAIRES: "Hélio de Caires",
                    JOSE_BARBAGELATA: "José Barbagelata",
                    OSCAR_OLAVE: "Oscar Olave"
                };

                vm.listaValorUnitario = {
                    INFERENCIA_ESTATISTICA: "Inferência Estatística",
                    DOMINIO_PLENO: "Domínio Pleno",
                    OUTRA_METODOLOGIA: "Outra Metodologia"
                };

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                vm.marginLeft143 = {'left': '143px'};
                vm.marginLeft130 = {'left': '130px'};
                vm.marginLeft112 = {'left': '112px'};

                var init = function () {

                };

                vm.$watch('avaliacao', function () {
                    vm.calcularValorTotalGleba();
                });

                vm.$watch('listaBenfeitoriaAvaliacao', function () {
                    if(vm.listaBenfeitoriaAvaliacao && vm.listaBenfeitoriaAvaliacao.length > 0) {
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                            if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao !== 'Complementar') {
                                vm.isBenfeitoriaEdificacao = true;
                            }
                            if (vm.isBenfeitoriaEdificacao) {
                                vm.isBenfeitoriaSemEdificacao = false;
                            } else {
                                vm.isBenfeitoriaSemEdificacao = true;
                            }
                        });
                    }else if(vm.unidadeAutonomaIncorporacao && vm.unidadeAutonomaIncorporacao.id){
                        vm.isUnidadeAutonoma = true
                    } else{
                        vm.isBenfeitoriaSemEdificacao = true;
                    }

                });

                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService
                        .converterNumeroPorExtenso(numero, 'real');
                };

                vm.limparDadosArquivosModelo = function () {
                    vm.avaliacao.resultadosAvaliacao.arquivosModelo = [];
                };

                vm.changeTipoValorUnitario = function () {
                    vm.avaliacao.loteParadigma = null;
                    vm.avaliacao.valorUnitarioAdotado = null;
                    vm.avaliacao.resultadosAvaliacao.relatoriosEstatisticos = null;
                    vm.avaliacao.resultadosAvaliacao.arquivosModelo = null;
                    vm.avaliacao.dominioPlenoFonte = null;
                    vm.avaliacao.dataDominioPleno = null;
                    vm.avaliacao.descricaoMetodologia = null;
                    vm.avaliacao.dataOutraMetodologia = null;
                    vm.avaliacao.outraMetodologiaAnexos = null;

                    if(vm.avaliacao.tipoValorUnitario === 'DOMINIO_PLENO'){
                        buscarValorDominioPlenoVigente();
                    } else {
                        vm.avaliacao.valorTotalGleba = 0;
                    }
                };

                vm.changeModeloAvaliacao = function () {
                    vm.avaliacao.valorPerdasArruamento = null;
                    vm.avaliacao.tipoValorUnitario = null;
                    vm.avaliacao.loteParadigma = null;
                    vm.avaliacao.valorUnitarioAdotado = null;
                    vm.avaliacao.resultadosAvaliacao.relatoriosEstatisticos = null;
                    vm.avaliacao.resultadosAvaliacao.arquivosModelo = null;
                    vm.avaliacao.dominioPlenoFonte = null;
                    vm.avaliacao.dataDominioPleno = null;
                    vm.avaliacao.descricaoMetodologia = null;
                    vm.avaliacao.dataOutraMetodologia = null;
                    vm.avaliacao.outraMetodologiaAnexos = null;
                    vm.avaliacao.lucroEmpregador = null;
                    vm.avaliacao.despesasUrbanizacao = null;
                    vm.avaliacao.descricaoFonte = null;
                    vm.avaliacao.anexos = null;
                    vm.avaliacao.valorTotalGleba = 0;

                };

                vm.calcularValorTotalGleba = function (){
                    if(vm.avaliacao.modeloAvaliacaoGleba === 'HELIO_CAIRES'){
                        if(vm.avaliacao.despesasUrbanizacao && vm.avaliacao.valorUnitarioAdotado) {
                            vm.avaliacao.valorTotalGleba = (0.62 * vm.areaTotalTerreno * vm.avaliacao.valorUnitarioAdotado) - (0.985 * vm.avaliacao.despesasUrbanizacao);
                        }
                    } else if (vm.avaliacao.modeloAvaliacaoGleba === 'JOSE_BARBAGELATA'){
                        if(vm.avaliacao.despesasUrbanizacao && vm.avaliacao.valorUnitarioAdotado && vm.avaliacao.valorPerdasArruamento) {
                            vm.avaliacao.valorTotalGleba = vm.areaTotalTerreno * (1 - vm.avaliacao.valorPerdasArruamento) * ((vm.avaliacao.valorUnitarioAdotado / 1.3) - (1.6 * vm.avaliacao.despesasUrbanizacao));
                        }
                    } else if (vm.avaliacao.modeloAvaliacaoGleba === 'OSCAR_OLAVE'){
                        if(vm.avaliacao.despesasUrbanizacao && vm.avaliacao.valorUnitarioAdotado && vm.avaliacao.valorPerdasArruamento && vm.avaliacao.lucroEmpregador) {
                            vm.avaliacao.valorTotalGleba = ((vm.areaTotalTerreno * (1 - vm.avaliacao.valorPerdasArruamento) * vm.avaliacao.valorUnitarioAdotado) / (1 + (vm.avaliacao.lucroEmpregador*0.01))) - vm.avaliacao.despesasUrbanizacao;
                        }
                    } else{
                        vm.avaliacao.valorTotalGleba = 0;
                    }
                }


                var buscarValorDominioPlenoVigente = function () {
                    dominioPlenoService.buscarValorDominioPlenoVigente(vm.identificacao.rip).then(function (resp) {
                        if (resp.data !== "") {
                            vm.dominioPleno = resp.data;
                            vm.avaliacao.dominioPlenoFonte = vm.dominioPleno.tipo === 'SPU' ? 'PVG/SPU' : vm.dominioPleno.tipo;
                            vm.avaliacao.valorUnitarioAdotado = vm.dominioPleno.valor;
                            vm.avaliacao.dataDominioPleno = vm.dominioPleno.dataInicial;
                            vm.calcularValorTotalGleba();
                        } else {
                            var confirm = $mdDialog.confirm({focusOnOpen: false})
                                .textContent($filter('translate')('mensagem-avaliacao-imovel-nao-cadastrada'))
                                .ok($filter('translate')('btn-salvar-rascunho-cadastrar-avaliacao'))
                                .cancel($filter('translate')('btn-nao-cadastrar-avaliacao'));
                            $mdDialog.show(confirm).then(function () {
                                vm.salvarComoRascunhoLaudoRelatorioCadastrarAvaliacao();
                                $timeout(function () {
                                    $state.go('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                        ripDetalhar: vm.identificacao.rip
                                    });
                                }, 1000);

                            }, function () {
                                $mdDialog.cancel();
                                vm.avaliacao.tipoValorUnitario = null;
                            });
                        }
                    });
                };

                vm.showDialogCalculoModelosGleba = function (ev, modeloAvaliacaoGleba) {
                    vm.modeloAvaliacaoGleba = modeloAvaliacaoGleba;
                    $mdDialog.show({
                        controller: function () {
                            return vm;
                        },
                        controllerAs: 'vm',
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogCalculoModelosGleba.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: true
                    });
                };

                vm.cancelModal = function () {
                    $mdDialog.cancel();
                };

                init();
            }
        };
    };

})();
(function () {

    directive.$inject = ["avaliacaoLaudoService", "numeroExtensoAvaliacaoService", "identificacaoLaudoRelatorioService", "avaliacaoEscopoCompartilhadoService", "cubAvaliacaoService", "$mdDialog", "$filter", "$window", "$state", "$timeout", "arquivoAvaliacaoService", "ComumFactory"];
    angular.module('su-avaliacao')
        .directive('avaliacaoEstimativa', directive);

    function directive(avaliacaoLaudoService, numeroExtensoAvaliacaoService, identificacaoLaudoRelatorioService, avaliacaoEscopoCompartilhadoService, cubAvaliacaoService, $mdDialog, $filter, $window, $state, $timeout, arquivoAvaliacaoService, ComumFactory) {
        return {
            restrict: 'EA',
            scope: {
                laudoRelatorioAvaliacao: '=',
                avaliacao: '=',
                quantificacaoCusto: '=',
                identificacao: '=',
                formularioValidacao: '=',
                listaBenfeitoriaAvaliacao: '=',
                unidadeAutonomaIncorporacao: '=',
                redirecionarInformarValorEdificacao: '=',
                redirecionarCalcularAreaEquivalente: '=',
                tipoValorLaudo: '=',
                detalharLaudoRelatorio: '=',
                anexoLaudoRelatorio: '=',
                uf: '=',
                areaTotalTerreno: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/avaliacaoEstimativa.tmpl.html',
            link: function ($scope) {

                var vm = $scope;

                vm.listaTiposValores = [];
                vm.listaNivelFundamentacao = [];
                vm.VALOR_MEDIO = 'VALOR_MEDIO';
                vm.VALOR_MINIMO = 'VALOR_MINIMO';
                vm.VALOR_MAXIMO = 'VALOR_MAXIMO';
                vm.OUTRO_VALOR = 'OUTRO_VALOR';

                vm.NAO_ATINGIDO = 'NAO_ATINGIDO';

                vm.CONTENT_TYPE_02 = 'CONTENT_TYPE_02';

                vm.IPO_BENFEITORIA = 'TIPO_BENFEITORIA';
                vm.LAUDO_RELATORIO = 'LAUDO_RELATORIO';
                vm.CUB = 'CUB';
                vm.OUTRA_FONTE = 'OUTRA_FONTE';
                vm.PVG_SPU = 'PVG_SPU';
                vm.PVG_MUNICIPIO = 'PVG_MUNICIPIO';
                vm.PVG_INCRA = 'PVG_INCRA';
                vm.formatterValorMedio = null;
                vm.formatterValorMinimo = null;
                vm.formatterValorMaximo = null;
                vm.formatterOutroValorAdotado = null;
                vm.valorTerrenoPorExtenso = null;

                vm.variacaoValorAdotado = null;
                vm.variacaoSuperiorValorMedio = null;
                vm.variacaoInferiorValorMedio = null;
                vm.tipoValorTerrenoAvaliacao = null;
                vm.dataAtual = new Date();

                vm.listaMoedas = [
                    {
                        id: 1,
                        codigo: '001',
                        nome: 'Real',
                        simbolo: 'R$',
                        codigoPais: '55',
                        pais: 'Brasil',
                        dataExclusao: new Date()
                    }, {
                        id: 2,
                        codigo: '002',
                        nome: 'Dolar',
                        simbolo: '$',
                        codigoPais: '00',
                        pais: 'Estados Unidos',
                        dataExclusao: new Date()
                    }
                ];

                vm.listaTiposDbi = {
                    CALCULADO: "Calculado",
                    JUSTIFICADO: "Justificado",
                    ARBITRADO: "Arbitrado"
                };

                vm.listaTiposDepreciacao = {
                    CUSTO_RECUPERACAO: "Custo de Recuperação",
                    ROSS_HEIDECKE: "Ross Heidecke",
                    ARBITRADO: "Arbitrado"
                };

                vm.listaTiposArea = {
                    AREA_REAL: "Área Real",
                    AREA_EQUIVALENTE_CONSTRUCAO: "Área Equivalente de Construção"
                };

                vm.listaTiposEstimativaCusto = {
                    UTILIZAR_ORCAMENTOS: "Utilizar Orçamentos",
                    APLICAR_PERCENTUAL: "Aplicar Percentual"
                };

                vm.icAdocaoEstimativa = true;
                vm.valorAdotadoSelecionado = 0;
                vm.valorTotalTerreno = 0;

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                var init = function () {
                    buscarTiposValores();
                    pesquisarEstadosConservacao();
                    preencheTiposCub();
                    populaTabelaDepreciacao();

                };

                vm.$watch('avaliacao', function () {
                    if (vm.avaliacao.tipoValorLaudo === vm.CUB) {
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                            if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao != 'Complementar') {
                                vm.valorTotalTerreno += benfeitoria.benfeitoriaImovel.valor;
                            }
                        });
                    } else if (vm.avaliacao.tipoValorLaudo === vm.OUTRA_FONTE) {
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                            if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao != 'Complementar') {
                                if(benfeitoria.benfeitoriaImovel.valorEdificacao) {
                                    vm.valorTotalTerreno += benfeitoria.benfeitoriaImovel.valorEdificacao.valor;
                                }
                            }
                        });

                    }

                    if (vm.avaliacao.tipoValorTerrenoAvaliacao != null) {
                        if (vm.avaliacao.tipoValorTerrenoAvaliacao != vm.OUTRA_FONTE) {
                            vm.changeTipoValorTerrenoAvaliacao(vm.avaliacao.tipoValorTerrenoAvaliacao);
                        }else{
                            if(vm.avaliacao.valorTerreno) {
                                vm.valorTerrenoPorExtenso = vm.convertNumeroPorExtenso(vm.avaliacao.valorTerreno);
                            }
                        }
                    }
                    if (!vm.avaliacao.anexos) {
                        vm.avaliacao.anexos = [];
                    }
                    if (vm.avaliacao.resultadosAvaliacao) {
                        vm.calcularVariacaoValorAdotado();
                    }
                    if (vm.avaliacao.resultadosAvaliacao) {
                        vm.avaliacao.resultadosAvaliacao.moeda = vm.listaMoedas[0];
                    }

                    if (vm.avaliacao.dataFonteValor) {
                        vm.avaliacao.dataFonteValor = new Date(vm.avaliacao.dataFonteValor);
                    } else {
                        vm.avaliacao.dataFonteValor = undefined;
                    }

                    vm.avaliacao.valorTotalTerreno = vm.valorTotalTerreno;
                });

                vm.$watch('areaTotalTerreno', function () {
                    if (!vm.areaTotalTerreno) {
                        vm.areaTotalTerreno = vm.laudoRelatorioAvaliacao.terrenoImovel.terreno.areaTotal;
                    }
                });

                vm.$watch('quantificacaoCusto', function () {
                    if (!vm.quantificacaoCusto) {
                        vm.quantificacaoCusto = {};
                        if (!vm.quantificacaoCusto.benfeitorias || vm.quantificacaoCusto.benfeitorias.length === 0) {
                            vm.quantificacaoCusto.benfeitorias = [];
                            angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                                if (benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao !== 'Complementar') {
                                    vm.quantificacaoCusto.benfeitorias.push({
                                        idBenfeitoriaImovel: benfeitoria.benfeitoriaImovel.id,
                                        idOriginal: benfeitoria.benfeitoriaImovel.idOriginal,
                                        codigo: benfeitoria.benfeitoriaImovel.codigo,
                                        nome: benfeitoria.benfeitoriaImovel.nome,
                                        tipoBenfeitoria: benfeitoria.benfeitoriaImovel.tipoBenfeitoria,
                                        area: benfeitoria.benfeitoriaImovel.areaConstruida
                                    });
                                }
                            });
                        }
                    } else if (vm.quantificacaoCusto.benfeitorias.length > 0) {
                        pesquisarEstadosConservacao(function () {
                            preencheTiposCub(function () {
                                angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                                    if (benfeitoria.estadoConservacao) {
                                        for (var i = 0; i < vm.listaEstadosConservacao.length; i++) {
                                            if (vm.listaEstadosConservacao[i].id === benfeitoria.estadoConservacao.id) {
                                                benfeitoria.estadoConservacao = vm.listaEstadosConservacao[i];
                                            }
                                        }
                                    }
                                });
                            });
                            vm.calcularCustoConstrucao();
                        });
                    }

                });

                vm.calcularValorBenfeitoria = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        if (vm.quantificacaoCusto.valorTaxaDepreciacao) {
                            benfeitoria.valorCalculado = ((benfeitoria.area * benfeitoria.valorCustoConstrucao) * (1 + (vm.quantificacaoCusto.valorTaxaBdi/100)) * (1 - (vm.quantificacaoCusto.valorTaxaDepreciacao/100)));
                        } else {
                            benfeitoria.valorCalculado = ((benfeitoria.area * benfeitoria.valorCustoConstrucao) * (1 + (vm.quantificacaoCusto.valorTaxaBdi/100)) * (1 - (benfeitoria.valorTaxaDepreciacao/100)));
                        }

                        benfeitoria.formatterValorCalculadoBenfeitoria = vm.convertNumeroPorExtenso(benfeitoria.valorCalculado);
                    });

                    vm.valorCustoTotalBenfeitoria = 0;
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        vm.valorCustoTotalBenfeitoria += benfeitoria.valorCalculado;
                    });

                    vm.formatterValorCustoTotalBenfeitoria = vm.convertNumeroPorExtenso(vm.valorCustoTotalBenfeitoria);
                };

                var buscarTiposValores = function () {
                    avaliacaoLaudoService.ordenacaoTipoValor().then(function (resp) {
                        vm.listaTiposValores = resp.data;
                    });
                };

                vm.alterandoMoedaValores = function () {
                    if (vm.avaliacao.resultadosAvaliacao) {
                        vm.formatterValorMedio = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMedio);
                        vm.formatterValorMinimo = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMinimo);
                        vm.formatterValorMaximo = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.valorMaximo);
                        vm.formatterOutroValorAdotado = vm.convertNumeroPorExtenso(vm.avaliacao.resultadosAvaliacao.outroValorAdotado);
                        vm.calcularVariacaoValorAdotado();
                    }
                };

                vm.calcularVariacaoValorAdotado = function () {
                    if (vm.avaliacao.resultadosAvaliacao.valorMedio && vm.avaliacao.resultadosAvaliacao.tipoValor == vm.OUTRO_VALOR
                        && vm.avaliacao.resultadosAvaliacao.outroValorAdotado) {
                        vm.variacaoValorAdotado = vm.avaliacao.resultadosAvaliacao.outroValorAdotado;
                        vm.variacaoSuperiorValorMedio = (vm.avaliacao.resultadosAvaliacao.valorMedio + (vm.avaliacao.resultadosAvaliacao.valorMedio * 0.01));
                        vm.variacaoInferiorValorMedio = (vm.avaliacao.resultadosAvaliacao.valorMedio - (vm.avaliacao.resultadosAvaliacao.valorMedio * 0.01));
                    }
                    vm.verificarAdocaoEstimativaValor();
                };

                vm.verificarAdocaoEstimativaValor = function () {
                    vm.icAdocaoEstimativa = true;
                    var bool = !(vm.variacaoValorAdotado && (vm.variacaoValorAdotado < vm.variacaoInferiorValorMedio || vm.variacaoValorAdotado > vm.variacaoSuperiorValorMedio));
                    if (bool) {
                        vm.icAdocaoEstimativa = false;
                    }
                };

                vm.determinarValorAdotadoSelecionado = function () {
                    if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MEDIO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMedio;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MINIMO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMinimo;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.VALOR_MAXIMO) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.valorMaximo;
                    } else if (vm.avaliacao.resultadosAvaliacao.tipoValor === vm.OUTRO_VALOR) {
                        vm.valorAdotadoSelecionado = vm.avaliacao.resultadosAvaliacao.outroValorAdotado;
                    }
                };

                vm.changeTipoValorTerrenoAvaliacao = function (tipo) {
                    vm.showValorTerreno = false;
                    vm.avaliacao.valorTerreno = null;
                    vm.avaliacao.fonteValor = null;
                    vm.avaliacao.dataFonteValor = null;
                    vm.avaliacao.anexos = null;
                    if (tipo != vm.OUTRA_FONTE) {
                        avaliacaoLaudoService.buscarValorTerreno(vm.identificacao.rip, tipo).then(function (resp) {
                            vm.pvg = resp.data;
                            vm.showValorTerreno = true;
                            if (vm.pvg === "") {
                                vm.showValorTerreno = false;
                                var confirm = $mdDialog.confirm({focusOnOpen: false})
                                    .textContent($filter('translate')('mensagem-incluir-fonte-valor-terreno'))
                                    .ok($filter('translate')('btn-sim'))
                                    .cancel($filter('translate')('btn-nao'));
                                $mdDialog.show(confirm).then(function () {
                                       var url = $state.href('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                                            ripDetalhar: vm.identificacao.rip
                                          });
                                           
                                      $window.open(url, '_blank');                                    
                                    
                                }, function () {
                                    $mdDialog.cancel();
                                });
                            }else{
                                vm.avaliacao.valorTerreno = vm.pvg.valorTerreno;
                            }
                        });
                    }
                };

                vm.changeTipoValorLaudo = function () {
                    vm.valorTotalTerreno = 0;
                    if (vm.listaBenfeitoriaAvaliacao && vm.listaBenfeitoriaAvaliacao.length > 0) {
                        angular.forEach(vm.listaBenfeitoriaAvaliacao, function (benfeitoria) {
                            benfeitoria.benfeitoriaImovel.valor = null;
                            benfeitoria.benfeitoriaImovel.padraoAcabamento = null;
                            benfeitoria.benfeitoriaImovel.tipoProjetoCub = null;
                            benfeitoria.benfeitoriaImovel.idadeAparente = null;
                            benfeitoria.benfeitoriaImovel.vidaUtil = null;
                            benfeitoria.benfeitoriaImovel.estadoConservacao = null;
                            benfeitoria.benfeitoriaImovel.valorCub = null;
                            benfeitoria.benfeitoriaImovel.valorCubDepreciado = null;
                            benfeitoria.benfeitoriaImovel.valorEdificacao = null;
                        });
                    } else if (vm.unidadeAutonomaIncorporacao) {
                        vm.unidadeAutonomaIncorporacao.valor = null;
                        vm.unidadeAutonomaIncorporacao.padraoAcabamento = null;
                        vm.unidadeAutonomaIncorporacao.tipoProjetoCub = null;
                    }
                };

                vm.limparBenfeitoriaCub = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        benfeitoria.cubDepreciacao = null;
                        benfeitoria.areaEquivalente = null;
                    });
                };

                vm.limparDadosBenfeitoriaDepreciacao = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        benfeitoria.vidaUtil = null;
                        benfeitoria.idadeAparente = null;
                        benfeitoria.estadoConservacao = null;
                        benfeitoria.valorTaxaDepreciacao = null;
                    });
                };

                var buscarValoresCubSiapa = function (tipoCub, callback) {
                    var date = new Date();

                    for (var i = 0; i < vm.tiposCub.length; i++) {
                        if (vm.tiposCub[i].nome === tipoCub) {
                            tipoCub = vm.tiposCub[i];
                        }
                    }

                    cubAvaliacaoService.buscarCubSiapa(vm.uf, date, tipoCub).then(function (resp) {
                        vm.valorCub = tipoCub.nome === 'CUB Médio' ? resp.data.cubs[0].valorMedio : resp.data.cubs[0].valor;

                        if (callback) {
                            callback();
                        }

                    }, function () {
                        vm.valorCub = null;
                    });
                };

                vm.calcularCustoConstrucao = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        if (vm.quantificacaoCusto.tipoArea === 'AREA_REAL') {
                            if (benfeitoria.cubDepreciacao != null && benfeitoria.valorOrcamentoElevadores != null && benfeitoria.valorOrcamentoInstalacoesEspeciais != null && benfeitoria.valorOrcamentoFundacoesEspeciais != null && benfeitoria.area != null) {
                                buscarValoresCubSiapa(benfeitoria.cubDepreciacao, function () {
                                    if (vm.valorCub) {
                                        if (vm.quantificacaoCusto.tipoEstimativaCusto === 'UTILIZAR_ORCAMENTOS') {
                                            benfeitoria.valorCustoConstrucao = vm.valorCub + ((benfeitoria.valorOrcamentoElevadores + benfeitoria.valorOrcamentoInstalacoesEspeciais + (benfeitoria.valorOrcamentoFundacoesEspeciais - benfeitoria.valorOrcamentoFundacoesDiretas)) / benfeitoria.area);
                                        } else {
                                            benfeitoria.valorCustoConstrucao = vm.valorCub * (1 + (benfeitoria.valorOrcamentoElevadores / 100)) * (1 + (benfeitoria.valorOrcamentoInstalacoesEspeciais / 100)) * (1 + (benfeitoria.valorOrcamentoFundacoesEspeciais / 100));
                                        }
                                        vm.calcularValorBenfeitoria();
                                    }
                                });

                            }
                        } else {
                            if (benfeitoria.areaEquivalente != null && benfeitoria.areaEquivalente.cubPadrao != null && benfeitoria.valorOrcamentoElevadores != null && benfeitoria.valorOrcamentoInstalacoesEspeciais != null && benfeitoria.valorOrcamentoFundacoesEspeciais != null && benfeitoria.areaEquivalente.areaConstruidaPadrao != null) {
                                buscarValoresCubSiapa(benfeitoria.areaEquivalente.cubPadrao, function () {
                                    if (vm.valorCub) {
                                        if (vm.quantificacaoCusto.tipoEstimativaCusto === 'UTILIZAR_ORCAMENTOS') {
                                            benfeitoria.valorCustoConstrucao = vm.valorCub + ((benfeitoria.valorOrcamentoElevadores + benfeitoria.valorOrcamentoInstalacoesEspeciais + (benfeitoria.valorOrcamentoFundacoesEspeciais - benfeitoria.valorOrcamentoFundacoesDiretas)) / benfeitoria.areaEquivalente.areaConstruidaPadrao);
                                        } else {
                                            benfeitoria.valorCustoConstrucao = vm.valorCub * (1 + (benfeitoria.valorOrcamentoElevadores / 100)) * (1 + (benfeitoria.valorOrcamentoInstalacoesEspeciais / 100)) * (1 + (benfeitoria.valorOrcamentoFundacoesEspeciais / 100));
                                        }
                                        vm.calcularValorBenfeitoria();
                                    }
                                });

                            }
                        }
                    });
                };

                vm.calcularValorEstimado = function () {
                    angular.forEach(vm.quantificacaoCusto.benfeitorias, function (benfeitoria) {
                        if (verificaIdadeAparente(benfeitoria.idadeAparente, benfeitoria.vidaUtil) && benfeitoria.estadoConservacao) {
                            if (benfeitoria.idadeAparente !== 0) {
                                var porcentagemVida = (benfeitoria.idadeAparente * 100) / benfeitoria.vidaUtil;
                                porcentagemVida = Math.round(porcentagemVida * 100) / 100;

                                porcentagemVida = arredondaNumero(porcentagemVida);

                                var valorSubtracao = benfeitoria.estadoConservacao.id === 9 ? 2 : 1;
                                benfeitoria.valorTaxaDepreciacao = vm.tabelaDepreciacao['coluna' + porcentagemVida][benfeitoria.estadoConservacao.id - valorSubtracao];
                            }
                        }
                    });
                };

                function arredondaNumero(porcentagemVida) {
                    var valor = 2 * Math.round(porcentagemVida / 2);

                    if (valor < 2) {
                        return 2;
                    } else if (valor > 100) {
                        return 100;
                    } else if (valor % 2 !== 0) {
                        return valor + 1;
                    } else {
                        return valor;
                    }

                }

                function verificaIdadeAparente(idadeAparente, vidaUtil) {
                    if (idadeAparente > vidaUtil) {
                        vidaUtil = '';
                        idadeAparente = '';
                        ComumFactory.mostrarMensagemError('A idade aparente do imóvel não pode ser maior que a vida útil.');
                        return false;
                    } else if (vidaUtil === 0) {
                        vidaUtil = '';
                        ComumFactory.mostrarMensagemError('A Vida Útil não pode ser igual a 0');
                        return false;
                    }
                    return true;
                }

                vm.calcularAreaEquilente = function (benfeitoria, detalhar) {
                    vm.redirecionarCalcularAreaEquivalente(benfeitoria, detalhar, "AVALIACAO", vm.TIPO_BENFEITORIA);
                };

                var pesquisarEstadosConservacao = function (callback) {
                    cubAvaliacaoService.buscarEstadosConservacao().then(function (resp) {
                        vm.listaEstadosConservacao = resp.data;
                        if (callback) {
                            callback();
                        }
                    });
                };

                vm.convertNumeroPorExtenso = function (numero) {
                    return numeroExtensoAvaliacaoService
                        .converterNumeroPorExtenso(numero, 'real');
                };

                vm.showDialogDepreciacaoHeidecke = function (ev) {
                    $mdDialog.show({
                        controller: function () {

                            vm.downloadPlanilha = function () {
                                window.open('assets/docs/Tab_Ross.pdf', "_blank");
                            }

                            return vm;
                        },
                        controllerAs: 'vm',
                        templateUrl: 'scripts-avaliacao/app/directives/partial/dialogDepreciacaoHeidecke.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: true
                    });
                };

                var preencheTiposCub = function (callback) {
                    vm.tiposCub = [];
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Baixo)', 1, 1, 'BAIXO', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Normal)', 1, 2, 'NORMAL', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-1, Padrão Residencial, 1 pavimento (Alto)', 1, 3, 'ALTO', 'R1_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Baixo)', 4, 1, 'BAIXO', 'PP4_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PP-4, Padrão Residencial, 4 pavimentos (Normal)', 4, 2, 'NORMAL', 'PP4_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Baixo)', 2, 1, 'BAIXO', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Normal)', 2, 2, 'NORMAL', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-8, Padrão Residencial, 8 pavimentos (Alto)', 2, 3, 'ALTO', 'R8_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Normal)', 3, 2, 'NORMAL', 'R16_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('R-16, Padrão Residencial, 16 pavimentos (Alto)', 3, 3, 'ALTO', 'R16_PADRAO_RESIDENCIAL'));
                    vm.tiposCub.push(new TipoCub('PIS, Padrão Residencial, Projeto de interesse social (Baixo)', 10, 1, 'BAIXO', 'PIS_PROJETO_INTERESSE_SOCIAL'));
                    vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Normal)', 5, 2, 'NORMAL', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
                    vm.tiposCub.push(new TipoCub('CAL-8, Padrão Comercial Andar Livre, 8 pavimentos (Alto)', 5, 3, 'ALTO', 'CAL8_PADRAO_COMERCIAL_ANDAR_LIVRE'));
                    vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Normal)', 6, 2, 'NORMAL', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-8, Padrão Comercial Salas e Lojas, 8 pavimentos (Alto)', 6, 3, 'ALTO', 'CSL8_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Normal)', 7, 2, 'NORMAL', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('CSL-16, Padrão Comercial Salas e Lojas, 16 pavimentos (Alto)', 7, 3, 'ALTO', 'CSL16_PADRAO_COMERCIAL_SALAS_LOJAS'));
                    vm.tiposCub.push(new TipoCub('GI, Padrão Galpão Indústrial, 1 pavimento', 8, 1, 'BAIXO', 'GI_PADRAO_GALPAO_INDUSTRIAL'));
                    vm.tiposCub.push(new TipoCub('RP1Q, Padrão Residencia Popular, 1 pavimento', 9, 1, 'BAIXO', 'RP1Q_PADRAO_RESIDENCIAL_POPULAR_QUARTO'));
                    vm.tiposCub.push(new TipoCub('CUB Médio', null, null, 'CUB_MEDIO', 'CUB_MEDIO'));

                    function TipoCub(nome, tipo, padrao, padraoAcabamento, tipoProjetoCub) {
                        this.nome = nome;
                        this.tipo = tipo;
                        this.padrao = padrao;
                        this.padraoAcabamento = padraoAcabamento;
                        this.tipoProjetoCub = tipoProjetoCub;
                    }

                    if (callback) {
                        callback();
                    }
                };

                function populaTabelaDepreciacao() {
                    vm.tabelaDepreciacao = {
                        coluna2: [1.02, 1.05, 3.51, 9.03, 18.9, 33.9, 53.1, 75.4],
                        coluna4: [2.08, 2.11, 4.55, 10, 19.8, 34.6, 53.6, 75.7],
                        coluna6: [3.18, 3.21, 5.62, 11, 20.7, 35.3, 54.1, 76],
                        coluna8: [4.32, 4.35, 6.73, 12.1, 21.6, 36.1, 54.6, 76.3],
                        coluna10: [5.5, 5.53, 7.88, 13.2, 22.6, 36.9, 55.2, 76.6],
                        coluna12: [6.72, 6.75, 9.07, 14.3, 23.6, 37.7, 55.8, 76.9],
                        coluna14: [7.98, 8.01, 10.3, 15.4, 24.6, 38.5, 56.4, 77.2],
                        coluna16: [9.28, 9.31, 11.6, 16.6, 25.7, 39.4, 57, 77.5],
                        coluna18: [10.6, 10.6, 12.9, 17.8, 26.8, 40.3, 57.6, 77.8],
                        coluna20: [12, 12, 14.2, 19.1, 27.9, 42.2, 58.3, 78.2],
                        coluna22: [13.4, 13.4, 15.6, 20.4, 29.1, 42.2, 59, 78.5],
                        coluna24: [14.9, 14.9, 17, 21.8, 30.3, 43.1, 59.6, 78.9],
                        coluna26: [16.4, 16.4, 18.5, 23.1, 31.5, 44.1, 60.4, 79.3],
                        coluna28: [17.9, 17, 20, 24.6, 32.8, 45.2, 61.1, 79.6],
                        coluna30: [19.5, 19.5, 21.5, 26, 34.1, 46.2, 61.8, 80],
                        coluna32: [21.1, 21.1, 23.1, 27.5, 35.4, 47.3, 62.6, 80.4],
                        coluna34: [22.8, 22.8, 24.7, 29, 36.8, 48.4, 63.4, 80.8],
                        coluna36: [24.5, 24.5, 26.4, 30.5, 38.1, 49.5, 64.2, 81.3],
                        coluna38: [26.2, 26.2, 28.1, 32.2, 39.6, 50.7, 65, 81.7],
                        coluna40: [28.8, 28.8, 29.9, 33.8, 41, 51.9, 65.9, 82.1],
                        coluna42: [29.8, 29.8, 31.6, 35.5, 42.5, 53.1, 66.7, 82.6],
                        coluna44: [31.7, 31.7, 33.4, 37.2, 44, 54.4, 67.6, 83.1],
                        coluna46: [33.6, 33.6, 35.2, 38.9, 45.6, 55.6, 68.5, 83.5],
                        coluna48: [35.5, 35.5, 37.1, 40.7, 47.2, 56.9, 69.4, 84],
                        coluna50: [37.5, 37.5, 39.1, 42.6, 48.8, 58.2, 70.4, 84.5],
                        coluna52: [39.5, 39.5, 41.9, 44, 50.5, 59.6, 71.3, 85],
                        coluna54: [41.6, 41.6, 43, 46.3, 52.1, 61, 72.3, 85.5],
                        coluna56: [43.7, 43.7, 45.1, 48.2, 53.9, 62.4, 63.3, 86],
                        coluna58: [45.8, 45.8, 47.2, 50.2, 55.6, 63.8, 74.3, 86.6],
                        coluna60: [48.8, 48.8, 49.3, 52.2, 57.4, 65.3, 75.3, 87.1],
                        coluna62: [50.2, 50.2, 51.5, 54.2, 59.2, 66.7, 75.4, 87.7],
                        coluna64: [52.5, 52.5, 53.7, 56.3, 61.1, 61.3, 77.5, 88.2],
                        coluna66: [54.8, 54.8, 55.9, 58.4, 69, 69.8, 78.6, 88.8],
                        coluna68: [57.1, 57.1, 58.2, 60.6, 64.9, 71.4, 79.7, 89.4],
                        coluna70: [59.5, 59.5, 60.5, 62.8, 66.8, 72.9, 80.8, 90.8],
                        coluna72: [62.2, 61.9, 62.9, 85, 68.8, 74.6, 81.9, 90.6],
                        coluna74: [64.4, 64.4, 65.3, 67.3, 70.8, 76.2, 83.1, 91.2],
                        coluna76: [66.9, 66.9, 67.7, 69.6, 72.9, 77.9, 84.3, 91.8],
                        coluna78: [69.4, 69.4, 72.7, 71.9, 74.9, 89.6, 85.5, 92.4],
                        coluna80: [72, 72, 72.7, 74.3, 77.1, 81.3, 86.7, 93.1],
                        coluna82: [74.6, 74.6, 75.3, 76.7, 79.2, 83, 88, 93.7],
                        coluna84: [77.3, 77.3, 77.8, 79.1, 81.4, 84.8, 89.2, 94.4],
                        coluna86: [80, 80, 80.5, 81.6, 83.6, 86.6, 90.5, 95],
                        coluna88: [82.7, 82.7, 83.2, 84.1, 85.8, 88.5, 91.8, 95.7],
                        coluna90: [85.5, 85.5, 85.9, 86.7, 88.1, 90.3, 93.1, 96.4],
                        coluna92: [88.3, 83.3, 88.6, 89.3, 90.4, 92.7, 94.5, 97.1],
                        coluna94: [91.2, 91.2, 91.4, 91.9, 92.8, 94.1, 95.8, 97.8],
                        coluna96: [94.1, 94.1, 94.2, 94.6, 95.1, 96, 97.2, 98.5],
                        coluna98: [97, 97, 97.1, 97.3, 97.6, 98, 98, 99.8],
                        coluna100: [100, 100, 100, 100, 100, 100, 100, 100]
                    };
                };

                vm.cancelModal = function () {
                    $mdDialog.cancel();
                };

                init();
            }
        };
    };

})();

(function () {

    directive.$inject = ["$filter", "arquivoAvaliacaoService", "imagemAvaliacaoService"];
    angular.module('su-avaliacao').directive('avaliacaoLaudoAnexos', directive);

    function directive($filter, arquivoAvaliacaoService, imagemAvaliacaoService) {
        return {
            restrict: 'EA',
            scope: {
                avaliacao: '=',
                formularioValidacao: '=',
                detalharLaudoRelatorio: '=',
                identificacao: '=',
                terreno: '=',
                laudo: '=',
                anexo: '='
            },
            templateUrl: 'scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/anexos.tmpl.html',
            link: function ($scope) {

                var vm = $scope;
                vm.qtdOrdem = 0;

                var init = function () {


                };

                vm.$watch('identificacao', function () {
                    if (vm.identificacao.tipo === 'RELATORIO') {
                        vm.tipo = 'Relatório de Valor Refêrencia';
                    } else if (vm.identificacao.tipo === 'LAUDO') {
                        vm.tipo = 'Laudo de Avaliação';
                    } else if (vm.identificacao.tipo === 'LAUDO_LOCACAO_ARRENDAMENTO') {
                        vm.tipo = 'Laudo de Locação';
                    }
                });

                vm.$watch('anexo', function () {
                    vm.qtdOrdem = 1;
                    for (var i = 0; i < vm.anexo.anexos.length; i++) {
                        if (vm.qtdOrdem < vm.anexo.anexos[i].ordem) {
                            vm.qtdOrdem = vm.anexo.anexos[i].ordem;
                        }
                    }
                    var j = vm.qtdOrdem + 1;
                    for (i = 0; i < vm.anexo.anexos.length; i++) {
                        if ((vm.anexo.anexos[i].ordem === -1) && ((vm.anexo.anexos[i].filhos && vm.anexo.anexos[i].filhos.length > 0) || vm.anexo.anexos[i].nome === 'Memorial Descritivo do Imóvel')) {
                            vm.anexo.anexos[i].ordem = angular.copy(j);
                            vm.qtdOrdem = angular.copy(j);
                            j++;
                        }
                    }
                    vm.anexo.anexos = $filter('orderBy')(vm.anexo.anexos, 'ordem');
                });

                vm.baixarArquivo = function (idArquivo) {
                    return arquivoAvaliacaoService.downloadArquivo(idArquivo);
                };

                vm.baixarImagem = function (idImagem, tipo) {
                    return imagemAvaliacaoService.downloadImagem(idImagem, tipo);
                };

                vm.incrementarOrdem = function (array, ordem) {
                    for (var i = 0; i < array.length; i++) {
                        if (array[i].ordem === ordem) {
                            array[i].ordem = array[i].ordem + 1;

                        } else if (array[i].ordem === (ordem + 1)) {
                            array[i].ordem = array[i].ordem - 1;
                        }
                    }

                    return $filter('orderBy')(array, 'ordem');
                };

                vm.decrementarOrdem = function (array, ordem) {
                    for (var i = 0; i < array.length; i++) {
                        if (array[i].ordem === ordem) {
                            array[i].ordem = array[i].ordem - 1;

                        } else if (array[i].ordem === (ordem - 1)) {
                            array[i].ordem = array[i].ordem + 1;
                        }
                    }

                    return $filter('orderBy')(array, 'ordem');
                };

                vm.incrementarOrdemFilhos = function (array, ordem) {
                    for (var i = 0; i < array.length; i++) {
                        if (array[i].ordem === ordem) {
                            array[i + 1].ordem = array[i + 1].ordem - 1;
                            array[i].ordem = array[i].ordem + 1;
                            break;
                        }
                    }

                    return $filter('orderBy')(array, 'ordem');
                };

                vm.decrementarOrdemFilhos = function (array, ordem) {
                    for (var i = 0; i < array.length; i++) {
                        if (array[i].ordem == (ordem - 1)) {
                            array[i + 1].ordem = array[i + 1].ordem - 1;
                            array[i].ordem = array[i].ordem + 1;
                            break;
                        }
                    }

                    return $filter('orderBy')(array, 'ordem');
                };

                init();
            }
        };
    }

})();
(function () {
    'use strict';
    /*global angular*/
    angular.module('su-avaliacao')
        .constant('MAPA_CONSTANTS', {
            NOTIFICADORES: {
                CENTRALIZAR_POR_MUNICIPIO: 'centralizarPorMunicipio',
                ALTERAR_VISIBILIDADE_BING: 'alterarVisibilidadeBing',
                PONTO_MARCADO: 'pontoMarcado',
                FEATURE_EXCLUIDA: 'featureExcluida',
                TERRENO_DESENHADO: 'terrenoDesenhado',
                UPLOAD_SHAPEFILE: 'uploadShapefileCiMapaDirective',
                UPLOAD_SHAPEFILE_BENFEITORIA: 'uploadShapefileBenfeitoriaCiMapaDirective',
                UPLOAD_SHAPEFILE_UNIDADE_AUTONOMA: 'uploadShapefileUnidadeAutonomaCiMapaDirective',
                VERIFICAR_PONTO_NA_ARESTA: 'verificarPontoNaAresta',
                BENFEITORIA_DESENHADA: 'benfeitoriaDesenhadaCiMapaDirective',
                SET_ID_BENFEITORIA: 'setIdBenfeitoriaCiMapaDirective',
                DESTACAR_BENFEITORIA: 'destacarBenfeitoriaCiMapaDirective',
                BENFEITORIA_EXCLUIDA: 'benfeitoriaExcluidaCiMapaDirective',
                DESFAZER_ALTERACAO_BENFEITORIA: 'desfazerAlteracaoBenfeitoriaCiMapaDirective',
                LIMPAR_DESENHO: 'limparDesenhoCiMapaDirective',
                RESULTADOPESQUISARIMOVEL: 'resultadoPesquisarImovelCiMapaDirective',
                DIRECIONAR_DETALHAR: 'direcionarDetalharCiMapaDirective',
                ADICIONAR_FEATURE_PONTO: 'adicionarFeaturePontoCiMapaDirective',
                ADICIONAR_FEATURE_TERRENO: 'adicionarFeatureTerrenoCiMapaDirective',
                ADICIONAR_FEATURE_BENFEITORIA: 'adicionarFeatureBenfeitoriaCiMapaDirective',
                ADICIONAR_FEATURE_UNIDADE_AUTONOMA: 'adicionarFeatureUnidadeAutonomaCiMapaDirective',
                PREENCHER_AREA_PROJECAO: 'preencherAreaProjecaoCiMapaDirective',
                SET_VETORIZAR_EM_TELA: 'setVetorizarEmTelaCiMapaDirective',
                DELETESHAPEFILECARTORIAL: 'deleteShapeFileCartorialCiMapaDirective',
                DELETESHAPEFILECADASTRAL: 'deleteShapeFileCadastralCiMapaDirective',
                LIMPAR_TIPO_IMOVEL: 'limparTipoImovel',
                REMOVER_PONTO_EDIFICACOES_TERRENO_UA: 'removerPontoEdificacoesTerrrenoUnidadeAutonomaCiMapaDirective',
                PONTO_IN_UNIDADE_AUTONOMA: 'pontoInUnidadeAutonoma',
                FEATURE_EXCLUIDA_UNIDADE_AUTONOMA: 'featureExcluidaUnidadeAutonomaCiMapaDirective',
                MARCAR_PONTO_POR_COORDENADAS: 'marcarPontoPorCoordenadasCiMapaDirective',
                ATUALIZAR_COORDENADAS_DO_PONTO: 'atualizarCoordenadasDoPontoCiMapaDirective',
                REMOVER_EDIFICACOES: 'removerEdificacoesCiMapaDirective',
                MARCAR_AMOSTRAS: 'marcarAmostrasCiMapaDirective',
                DESENHAR_BENFEITORIAS: 'desenharBenfeitoriasCiMapaDirective',
                LIMPAR_BENFEITORIAS: 'limparBenfeitoriasCiMapaDirective',
                CENTRALIZAR_MAPA: 'centralizarMapaCiMapaDirective',
                MARCAR_IMOVEL: 'marcarImovelCiMapaDirective',
                MARCAR_PONTOS_AVALIACAO_TERRENO: 'marcarPontoAvaliacaoTerrenoCiMapaDirective',
            },
            FEATURE_ID: {
                PONTO_IMOVEL: 'pontoImovel',
                PONTO_AMOSTRA: 'pontoAmostra',
                TERRENO_CADASTRAL: 'terrenoCadastral',
                TERRENO_CADASTRAL_SOBREPOSTO: 'terrenoCadastralSobreposto',
                TERRENO_CARTORIAL_SOBREPOSTO: 'terrenoCartorialSobreposto',
                CENTRO_SEGMENTO: 'centroSegmento',
                TERRENO_CARTORIAL: 'terrenoCartorial',
                ACESSAO_BENFEITORIA: 'acessaoBenfeitoria',
                UNIDADE_AUTONOMA: 'unidadeAutonoma',
                TERRENO_BENFEITORIA_SOBREPOSTO: 'terrenoBenfeitoriaSobreposto',
                TERRENO_BENFEITORIA_INVALIDO: 'terrenoBenfeitoriaInvalido'
            },
            GEOMETRY: {
                GEOMETRIA_INICIAL: [-55.41, -15.82],
                CENTRALIZAR_IMOVEL: [1246, 500]
            },
            TIPO_IMOVEL: {
                UNIDADE_AUTONOMA: "UNIDADE AUTÔNOMA"
            },
            LAYERS: {
                PONTO_UNIDADE_AUTONOMA: 'pontoUnidadeAutonoma'
            }
        });
}());

(function(){
    'use strict';
    service.$inject = ["formularioAvaliacaoService"];
    angular
        .module('su-avaliacao')
        .factory('validacaoLaudoRelatorioAvaliacaoService', service);

    function service(formularioAvaliacaoService) {

        var formularioServiceInstance = formularioAvaliacaoService.getInstance();
        var validarFormulario = formularioServiceInstance.validarFormulario;

        var validandoFormulario = function(formulario) {
            validarFormulario(formulario);
            return !formulario.$valid;
        };


        return {
            validandoFormulario: validandoFormulario
        };
    }

})();
(function(){
    'use strict';
    service.$inject = ["$http", "dominioAvaliacaoService", "ComumFactory", "$filter"];
    angular
        .module('su-avaliacao')
        .factory('usuarioAvaliacaoService', service);

    function service($http, dominioAvaliacaoService, ComumFactory, $filter) {

        var URL_USUARIO = '/avaliacao/api/usuario';

        var getUsuarioLogado = function() {
            return $http.get(URL_USUARIO);
        };

        var getJurisdicao = function() {
            return $http.get(URL_USUARIO + '/jurisdicoes');
        };

        var getUsuarioSistema = function(cpf) {
            return $http.get(URL_USUARIO + '/buscar-usuario-sistema/' + cpf);
        };

        var getOrgaos = function() {
            return $http.get(URL_USUARIO + '/buscar-orgaos');
        };
        var getUnidades = function(orgao) {
            return $http.get(URL_USUARIO + '/buscar-unidades/' + orgao);
        };

        var getUnidadesUsuario = function() {
            return $http.get(URL_USUARIO + '/buscar-unidades-usuario');
        };

        var mesmosUsuarios = function(acao, usuarioLogado) {
            if (!dominioAvaliacaoService.ehAmbienteLocal()) {

                var isCpfResponsavel = acao.cpfResponsavel !== null && acao.cpfResponsavel === usuarioLogado.cpf;
                var isResponsavelNome = acao.cpfResponsavel === null && acao.responsavel === usuarioLogado.nome;

                if (isCpfResponsavel || isResponsavelNome) {
                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-erro-pendencia-de-validacao-usuario'));
                    return true;
                }

                return false;
            }
        }

        return {
            getUsuarioLogado: getUsuarioLogado,
            getJurisdicao: getJurisdicao,
            getOrgaos: getOrgaos,
            getUnidades: getUnidades,
            getUnidadesUsuario: getUnidadesUsuario,
            getUsuarioSistema: getUsuarioSistema,
            mesmosUsuarios: mesmosUsuarios
        };
    }

})();

(function(){
    'use strict';
    service.$inject = ["$http"];
    angular
        .module('su-avaliacao')
        .factory('trechoAvaliacaoService', service);

    function service ($http) {

        var URL_TRECHO = 'avaliacao/api/trecho';

        var buscarTrechosPorIdLogradouro = function(idLogradouro) {
            return $http.get(URL_TRECHO + '/buscar/siapa/' + idLogradouro);
        };

        return {
            buscarTrechosPorIdLogradouro: buscarTrechosPorIdLogradouro
        };
    }

})();
(function () {
'use strict';
sincronizaAvaliacao.$inject = ["$http", "$state", "MensagensService"];
angular
  .module('su-avaliacao')
  .factory('SincronizarAvaliacaoService', sincronizaAvaliacao);

  var URL_LAUDO_AVALIACAO = '/avaliacao/api/laudo-avaliacao';

function sincronizaAvaliacao ($http, $state, MensagensService) {
  var sincronizarAvaliacao = function (id) {
    return $http.get(URL_LAUDO_AVALIACAO + '/sincronizar/' + id);
  };

  var consultaMotivoNaoSincronizavel = function (id) {
    return $http.get(URL_LAUDO_AVALIACAO + '/imovel-avaliacao/motivo-nao-sincronizavel/laudo/' + id);
  };

  var sincronizacao = function (id) {
    var resultado = sincronizarAvaliacao(id);
        resultado.then(function (resp) {
          var status = resp.status;
          if (status === 200) {
            avaliacaoSincronizada(id)
              .then(function (resp) {
                var descricaoPendencia = resp.data.descricaoPendencia;
                if (descricaoPendencia === null || descricaoPendencia === undefined) {
                  MensagensService.exibirMensagemSucesso('Avaliação sincronizada com sucesso');
                } else {
                  MensagensService.exibirMensagemErro('Avaliação não sincronizada.');
                } 
              })
          }else if (status === 204) {
            consultaMotivoNaoSincronizavel(id).then(function (retorno){
              var motivoObj = retorno.data;
              MensagensService.exibirMensagemErro('Imóvel com avaliação não sincronizável: '+motivoObj.motivo);  
            })
            
          }else {
            MensagensService.exibirMensagemErro('Erro na sincronização.');
          }
      });
      return resultado;
  };

  var avaliacaoSincronizada = function (id) {
      return $http.get(URL_LAUDO_AVALIACAO + '/consultar/pendenciaSincronismo/' + id);
  };

  return {
    sincronizacao:sincronizacao,
    avaliacaoSincronizada:avaliacaoSincronizada,
    sincronizarAvaliacao: sincronizarAvaliacao
  };
}
})();

(function(){
    'use strict';
    angular
        .module('su-avaliacao')
        .factory('numeroExtensoAvaliacaoService', service);

    function service () {
        var ex = [
            ["zero", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove", "dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"],
            ["dez", "vinte", "trinta", "quarenta", "cinqüenta", "sessenta", "setenta", "oitenta", "noventa"],
            ["cem", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"],
            ["mil", "milhão", "bilhão", "trilhão", "quadrilhão", "quintilhão", "sextilhão", "setilhão", "octilhão", "nonilhão", "decilhão", "undecilhão", "dodecilhão", "tredecilhão", "quatrodecilhão", "quindecilhão", "sedecilhão", "septendecilhão", "octencilhão", "nonencilhão"]
        ];

        var converterNumeroPorExtenso = function(numero, moeda) {
            var c = numero.toString().replace('.', ',');
            var a, n, v, i, n = c.replace(c ? /[^,\d]/g : /\D/g, "").split(","), e = " e ", $ = moeda, d = "centavo", sl;
            for(var f = n.length - 1, l, j = -1, r = [], s = [], t = ""; ++j <= f; s = []){
                j && (n[j] = (("." + n[j]) * 1).toFixed(2).slice(2));
                if(!(a = (v = n[j]).slice((l = v.length) % 3).match(/\d{3}/g), v = l % 3 ? [v.slice(0, l % 3)] : [], v = a ? v.concat(a) : v).length) continue;
                for(a = -1, l = v.length; ++a < l; t = ""){
                    if(!(i = v[a] * 1)) continue;
                    i % 100 < 20 && (t += ex[0][i % 100]) ||
                    i % 100 + 1 && (t += ex[1][(i % 100 / 10 >> 0) - 1] + (i % 10 ? e + ex[0][i % 10] : ""));
                    s.push((i < 100 ? t : !(i % 100) ? ex[2][i == 100 ? 0 : i / 100 >> 0] : (ex[2][i / 100 >> 0] + e + t)) +
                        ((t = l - a - 2) > -1 ? " " + (i > 1 && t > 0 ? ex[3][t].replace("ão", "ões") : ex[3][t]) : ""));
                }
                a = ((sl = s.length) > 1 ? (a = s.pop(), s.join(" ") + e + a) : s.join("") || ((!j && (n[j + 1] * 1 > 0) || r.length) ? "" : ex[0][0]));
                a && r.push(a + (c ? (" " + (v.join("") * 1 > 1 ? j ? d + "s" : (/0{6,}$/.test(n[0]) ? "de " : "") + validarReplaceNomeMoedaPlural($) : j ? d : $)) : ""));
            }
            return r.join(e);
        };

        var validarReplaceNomeMoedaPlural = function(nome) {
            if (nome=='real') {
                return 'reais';
            } else if(nome=='dolar') {
                return 'dolares';
            }
        };

        return {
            converterNumeroPorExtenso: converterNumeroPorExtenso
        };
    }

})();
(function () {
    "use strict";
    mensagensService.$inject = ["$mdToast"];
    angular.module("su-avaliacao").factory("MensagensService", mensagensService);

    function mensagensService($mdToast) {

        var TEMAS = {
            ERRO: "error-toast",
            SUCESSO: "success-toast"
        };

        var MENSAGENS = {
            CAMPOS_OBRIGATORIOS: "Necessário preencher os campos obrigatórios"
        };

        function exibirMensagem(msg, tema) {
            $mdToast.show(
                $mdToast.simple()
                    .content(msg)
                    .theme(tema)
                    .position('top right')
                    .hideDelay(15000)
            );
        }

        function mensagemErro(msg) {
            exibirMensagem(msg, TEMAS.ERRO);
        }

        function mensagemSucesso(msg) {
            exibirMensagem(msg, TEMAS.SUCESSO);
        }

        function mensagemCamposObrigatorios() {
            exibirMensagem(MENSAGENS.CAMPOS_OBRIGATORIOS, TEMAS.ERRO);
        }

        return {
            exibirMensagemErro: mensagemErro,
            exibirMensagemSucesso: mensagemSucesso,
            exibirMensagemCamposObrigatorios: mensagemCamposObrigatorios
        };
    }
})();

(function(){
    'use strict';
    service.$inject = ["ol", "$filter"];
    angular
        .module('su-avaliacao')
        .factory('mapaAvaliacaoService', service);

    function service(ol, $filter) {

        var getOptions = function() {
            return {
                tipLabel: 'Expandir Mapa',
                zoomInTipLabel: 'Aumentar Zoom',
                zoomOutTipLabel: 'Diminuir Zoom'
            };
        };

        var getOverlay = function(elementOverlay) {
            return new ol.Overlay({
                element: elementOverlay,
                stopEvent: false,
                offset: [0, -50]
            });
        };

        var getSource = function() {
            return new ol.source.Vector({wrapX: false});
        };

        var getIcon = function() {
            return new ol.style.Icon({
                anchor: [0.5, 46],
                anchorXUnits: 'fraction',
                anchorYUnits: 'pixels',
                opacity: 0.75,
                src: 'assets/img/icons/pointer.png'
            })
        };

        var getFill = function() {
            return new ol.style.Fill({
                color: 'rgba(255, 222, 0, 0.3)'
            })
        };

        var getStroke = function() {
            return new ol.style.Stroke({
                color: '#ffde00',
                width: 3
            })
        };

        var getVector = function(source, iconStyle) {
            return new ol.layer.Vector({
                source: source,
                style: iconStyle
            })
        };

        var getViewPadrao = function() {
            return new ol.View({
                center: ol.proj.fromLonLat([-48.0556, -16.0193]),
                zoom: 3,
                projection: "EPSG:3857"
            })
        };

        var getTileOSM = function() {
            return new ol.layer.Tile({
                source: new ol.source.OSM()
            })
        };

        var getMap = function(layers, view) {
            return new ol.Map({
                target: 'map',
                layers: layers,
                view: view
            })
        };

        var criarFeature = function(pontoImovel) {
            return new ol.Feature({
                geometry : new ol.format.GeoJSON({defaultProjection: 'EPSG:4674'}).readGeometry(pontoImovel)
            });
        };

        var getTemplateOverlay = function(dadosImovel) {
            var html = '<div style="color:black; background-color:white; width:300px;">' +
                '<md-tooltip style="color:black; background-color:white;">' +
                '<md-content layout-padding>' +

                '<div layout="row" flex="100">' +
                    '<div flex="40" class="flexMapaAvaliacaoServiceLeft" style="float:left">' +
                        '<img src="http://i.imgur.com/YV0WS3k.png"/>' +
                    '</div>' +
                    '<div layout="column" flex="40" class="flexMapaAvaliacaoServiceRight" style="float:right">' +
                        '<div layout="row">' +
                            '<label flex="50">' +
                                '<span class="negritoTexto">' + $filter('translate')('tr-rip') + ':</span>' +
                                '<span> ' + $filter('rip')(dadosImovel.rip) + '</span>' +
                            '</label>' +
                        '</div>' +
                        '<div layout="row" flex>' +
                            '<label flex="100">' +
                                '<span class="negritoTexto">' + $filter('translate')('tr-endereco') + ':</span>' +
                                '<span> ' + dadosImovel.enderecamento.bairro + ', '
                                    + dadosImovel.enderecamento.municipio + '/'
                                    + dadosImovel.enderecamento.uf + '</span>' +
                            '</label>' +
                        '</div>' +
                        '<div layout="row" flex>' +
                            '<label flex="100">' +
                                '<span class="negritoTexto">' + $filter('translate')('tr-cep') + ':</span>' +
                                '<span>&nbsp;' + $filter('cep')(dadosImovel.enderecamento.cep) + '</span>' +
                            '</label>' +
                        '</div>' +
                    '</div>' +

                '</div>';

            if (dadosImovel.valorImovelLaudoRelatorio) {
                html += '<div layout="row" flex>' +
                            '<label flex="100">' +
                                '<span class="negritoTexto">' + $filter('translate')('tr-valor-do-imovel-laudo-relatorio') + ':</span>' +
                                '<span> ' + $filter('currency')(dadosImovel.valorImovelLaudoRelatorio).replace('R$', '') + '</span>' +
                            '</label>' +
                        '</div>';
            }
            /*if (dadosImovel.dataAssinaturaLaudoRelatorio) {
                html += '<div layout="row" flex>' +
                    '<label flex="50">' +
                    '<span class="negritoTexto">' + $filter('translate')('tr-valido-para-alienacao') + ':</span>' +
                    '</label>' +
                    '</div>';
            }*/
            if (dadosImovel.valorTerrenoPvgSpu) {
                html += '<div layout="row" flex>' +
                            '<label flex="50">' +
                                '<span class="negritoTexto">' + $filter('translate')('tr-valor-do-terreno-pvg-spu') + ':</span>' +
                                '<span> ' + $filter('currency')(dadosImovel.valorTerrenoPvgSpu).replace('R$', '') + '</span>' +
                            '</label>' +
                        '</div>';
            }
            if (dadosImovel.valorTerrenoPvgMunicipio) {
                html += '<div layout="row" flex>' +
                    '<label flex="50">' +
                    '<span class="negritoTexto">' + $filter('translate')('tr-valor-do-terreno-pvg-municipio') + ':</span>' +
                    '<span> ' + $filter('currency')(dadosImovel.valorTerrenoPvgMunicipio).replace('R$', '') + '</span>' +
                    '</label>' +
                    '</div>';
            }
            if (dadosImovel.valorTerrenoPvgIncra) {
                html += '<div layout="row">' +
                            '<label flex="50">' +
                                '<span class="negritoTexto">' + $filter('translate')('tr-valor-do-terreno-pvg-incra') + ':</span>' +
                                '<span> ' + $filter('currency')(dadosImovel.valorTerrenoPvgIncra).replace('R$', '') + '</span>' +
                            '</label>' +
                        '</div>';
            }
            html += '</md-content>' +
                '</md-tooltip>' +
            '</div>';

            return html;
        };

        return {
            getOptions: getOptions,
            getOverlay: getOverlay,
            getSource: getSource,
            getIcon: getIcon,
            getFill: getFill,
            getStroke: getStroke,
            getVector: getVector,
            getTileOSM: getTileOSM,
            getViewPadrao: getViewPadrao,
            getMap: getMap,
            criarFeature: criarFeature,
            getTemplateOverlay: getTemplateOverlay
        };
    }

})();
(function(){
    'use strict';
    service.$inject = ["$http"];
    angular
        .module('su-avaliacao')
        .factory('logradouroPvgAvaliacaoService', service);

    function service ($http) {

        var URL_LOGRADOURO_PVG = 'avaliacao/api/logradouro/pvg';

        var buscarListaLogradourosPorCepGenerico = function(cep) {
            return $http.get(URL_LOGRADOURO_PVG + '/buscar/lista/logradouro/' + cep);
        };

        var buscarLogradouroPorCepNaoGenerico = function(cep) {
            return $http.get(URL_LOGRADOURO_PVG + '/buscar/lista/logradouro/' + cep);
        };

        return {
            buscarListaLogradourosPorCepGenerico: buscarListaLogradourosPorCepGenerico,
            buscarLogradouroPorCepNaoGenerico: buscarLogradouroPorCepNaoGenerico
        };
    }

})();
(function () {
    'use strict';
    angular.module('su-avaliacao').factory('informacaoAnexoService', ["$http", function ($http) {

        var URL_ARQUIVO = '/avaliacao/api/informacao-anexo';

        var deletarInformacaoAnexo = function (idArquivo) {
            return $http.delete(URL_ARQUIVO + '/' + idArquivo);
        };

        return {
            deletarInformacaoAnexo: deletarInformacaoAnexo
        };

    }]);
})();

(function () {
	'use strict';
	angular.module('su-avaliacao')
		.factory("imovelContabilidadeService", ["$http", function($http){
			var URL_BASE = '/avaliacao/api/valor-contabil';

			var buscarHistorico = function(data) {
				return $http.get(URL_BASE + '/historico?rip=' + data.rip + "&dataInicio=" + data.dataInicial + "&dataFim=" + data.dataFinal);
			};

			var buscarDepreciacao = function(data) {
				return $http.get(URL_BASE + '/depreciacao?rip=' + data.rip + "&dataInicio=" + data.dataInicial + "&dataFim=" + data.dataFinal);
			};

            return {
				buscarHistorico: buscarHistorico,
				buscarDepreciacao: buscarDepreciacao
			}
		}])
})();
(function () {
	'use strict';
	angular.module('su-avaliacao')
		.factory("imovelAvaliacaoService", ["$http", function($http){
			var URL_INCORPORACAO = '/avaliacao/api/incorporacao';

			var consultarImovelPorRip = function(rip) {
				return $http.get(URL_INCORPORACAO + '/detalhar/' + rip);
			};

			var buscarFotosImovel = function(rip) {
				return $http.get(URL_INCORPORACAO + '/buscar/imagens/imovel/' + rip);
			};

			var buscarBenfeitoriasSuPorRip = function(rip) {
				return $http.get(URL_INCORPORACAO + '/buscar/benfeitorias/imovel/' + rip);
			};

			var buscarUnidadeAutonomaSuPorRip = function(rip) {
				return $http.get(URL_INCORPORACAO + '/buscar/unidade-autonoma/' + rip);
			};

			var buscarDadosDetalharImovelLaudoRelatorioRip = function(rip) {
				return $http.get(URL_INCORPORACAO + '/buscar/detalhar/imovel/laudo/' + rip);
			};

			var buscarDadosDetalharProprietarioLaudo = function(idImovel) {
				return $http.get(URL_INCORPORACAO + '/buscar/proprietarioImovel/' + idImovel);
			};

			var buscarDadosGeometriaIncorporacao = function(rip) {
				return $http.get(URL_INCORPORACAO + '/buscar/geometriaIncorporacao/' + rip);
			};			

			var ectCepService =  function ($resource, urlBase) {
				return $resource(URL_INCORPORACAO + 'ect/localidade/porCep/:cep', { cep: '@cep' });
			};

            var buscarGeometria = function(endereco) {
                var enderecamentoImovel = {
                    uf: endereco.uf,
                    municipio: endereco.municipio,
                    bairro: endereco.bairro
                };

                return $http.post(URL_INCORPORACAO+"/cartografia/geometria/por-endereco", enderecamentoImovel);
            };

            return {
				consultarImovelPorRip : consultarImovelPorRip,
				buscarFotosImovel: buscarFotosImovel,
				buscarBenfeitoriasSuPorRip: buscarBenfeitoriasSuPorRip,
				buscarUnidadeAutonomaSuPorRip: buscarUnidadeAutonomaSuPorRip,
				buscarDadosDetalharImovelLaudoRelatorioRip: buscarDadosDetalharImovelLaudoRelatorioRip,
				buscarDadosDetalharProprietarioLaudo: buscarDadosDetalharProprietarioLaudo,
				buscarDadosGeometriaIncorporacao: buscarDadosGeometriaIncorporacao,
                buscarGeometria:buscarGeometria,
				ectCepService: ectCepService
			}
		}])
})();
(function () {
    'use strict';
    service.$inject = ["Upload", "$http"];
    angular
        .module('su-avaliacao')
        .factory('imagemAvaliacaoService', service);

    function service(Upload, $http) {

        var URL_IMAGEM_IMOVEL = 'avaliacao/api/imagemImovel';
        var URL_IMAGEM_BENFEITORIA = 'avaliacao/api/imagemBenfeitoria';
        var URL_IMAGEM_UNIDADE_AUTONOMA = 'avaliacao/api/imagemUnidadeAutonoma';

        var IMAGEM_IMOVEL = 'IMAGEM_IMOVEL';
        var IMAGEM_BENFEITORIA = 'IMAGEM_BENFEITORIA';
        var IMAGEM_UNIDADE_AUTONOMA = 'IMAGEM_UNIDADE_AUTONOMA';

        var MEDIA_TYPE_PNG = 'image/png';

        var uploadImagem = function ($file, tipoFoto) {
            var URL_IMAGEM = validarUrlImagem(tipoFoto);
            return Upload.upload({
                url: URL_IMAGEM + '/upload',
                data: {
                    file: $file
                }
            });
        };

        var deletarImagem = function (idImagem) {
            return $http.delete(URL_IMAGEM_IMOVEL + '/' + idImagem);
        };

        var validarUrlImagem = function (tipoFoto) {
            if (tipoFoto == IMAGEM_IMOVEL) {
                return URL_IMAGEM_IMOVEL;
            } else if (tipoFoto == IMAGEM_BENFEITORIA) {
                return URL_IMAGEM_BENFEITORIA;
            } else if (tipoFoto == IMAGEM_UNIDADE_AUTONOMA) {
                return URL_IMAGEM_UNIDADE_AUTONOMA;
            }
        };

        var recuperarImagensImovel = function (idImovel) {
            return $http.get(URL_IMAGEM_IMOVEL + '/recuperar/' + idImovel);
        };

        var recuperarImagensBenfeitorias = function (idBenfeitorias) {
            return $http.get(URL_IMAGEM_BENFEITORIA + '/recuperar/' + idBenfeitorias);
        };

        var recuperarImagensUnidadeAutonoma = function (idUnidadeAutonoma) {
            return $http.get(URL_IMAGEM_UNIDADE_AUTONOMA + '/recuperar/' + idUnidadeAutonoma);
        };

        var gerarPreviewImagem = function (base64, contentType) {
            return 'data:' + (contentType ? contentType : MEDIA_TYPE_PNG) + ';base64,' + base64;
        };

        var downloadImagem = function (idImagem, tipoFoto) {
            return validarUrlImagem(tipoFoto) + '/download/' + idImagem;
        };

        return {
            uploadImagem: uploadImagem,
            deletarImagem: deletarImagem,
            recuperarImagensImovel: recuperarImagensImovel,
            recuperarImagensBenfeitorias: recuperarImagensBenfeitorias,
            recuperarImagensUnidadeAutonoma: recuperarImagensUnidadeAutonoma,
            gerarPreviewImagem: gerarPreviewImagem,
            downloadImagem: downloadImagem
        };
    }

})();
(function(){

    'use strict';

    formularioService.$inject = ["ComumFactory", "$filter"];
    angular
        .module('su-avaliacao')
        .service('formularioAvaliacaoService', formularioService);

    function formularioService(ComumFactory, $filter) {

        var LAUDO_RELATORIO = $filter('translate')('laudo-relatorio');


        var instance = function(){

            var abasFormularios  = {
                laudoRelatorio : false
            };

             var verificarValidade = function(form){
                return !form.$valid;
            };

             var verificarObrigatoriedade = function(form){

                var error = form.$error;
                var invalido = false;

                angular.forEach(error.required, function (field) {
                    if(field.$invalid){

                        invalido = true;

                        if(field.$name.indexOf(LAUDO_RELATORIO) != -1 && !abasFormularios.laudoRelatorio){
                            abasFormularios.laudoRelatorio = true;
                        }

                    }
                });

                return invalido;
            };

            this.validarFormulario = function(form){

                form.$setSubmitted();
                var invalido = verificarObrigatoriedade(form);

                if(invalido){
                    ComumFactory.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                }

                return !invalido;
            };

            this.abasFormularios = abasFormularios;

        };

        return {
            getInstance: function(){
                return new instance();
            }
        }
    }

})();

(function(){
    'use strict';
    service.$inject = ["$http"];
    angular
        .module('su-avaliacao')
        .factory('dominioAvaliacaoService', service);

    function service ($http) {

        var URL_DOMINIO = 'avaliacao/api/dominio';

        var consultarTiposImovelSU = function() {
            return $http.get(URL_DOMINIO + '/buscar/tipo/imovel/all');
        };

        var consultarNaturezasImovelSU = function() {
            return $http.get(URL_DOMINIO + '/buscar/naturezaImovel/all');
        };

        var consultarImovelPaisesSU = function() {
            return $http.get(URL_DOMINIO + '/buscar/paises/all');
        };

        var consultarTiposLogradouroSU = function() {
            return $http.get(URL_DOMINIO + '/buscar/tipo/logradouro/all');
        };

        var pesquisarUfsIntegrador = function () {
            return $http.get(URL_DOMINIO + '/buscar/uf/all')
        };

        var pesquisarMuniciposPorUfIntegrador = function(uf) {
            return $http.get(URL_DOMINIO + '/buscar/municipio/porUf/' + uf);
        };

        var pesquisarLocalidadePorCepIntegrador = function(cep) {
            return $http.get(URL_DOMINIO + '/buscar/localidade/' + cep);
        };

        var pesquisarCartoriosPorUfIntegrador = function(uf) {
            return $http.get(URL_DOMINIO + '/buscar/cartorios/' + uf);
        };

        var pesquisarCartoriosPorUfMunicipioIntegrador = function(uf, codigoMunicipio) {
            return $http.get(URL_DOMINIO + '/buscar/cartorios/' + uf + '/' + codigoMunicipio);
        };

        var pesquisarTiposProprietarioRealIncorporacao = function() {
            return $http.get(URL_DOMINIO + '/buscar/tipo-proprietario-real/all');
        };

        var pesquisarTiposProprietarioParcialIncorporacao = function() {
            return $http.get(URL_DOMINIO + '/buscar/tipo-proprietario-parcial/all');
        };

        var pesquisarTiposUtilizacaoDestinacao = function() {
            return $http.get(URL_DOMINIO + '/buscar/tipo-utilizacao/all')
        };

        var pesquisarSubTiposUtilizacaoDestinacao = function() {
            return $http.get(URL_DOMINIO + '/buscar/sub-tipo-utilizacao/all')
        };

        var pesquisarDadosUtilizacoesDestinacao = function(rip) {
            return $http.get(URL_DOMINIO + '/buscar/dados/destinacoes/' + rip);
        };

        var ehAmbienteDesenvolvimento = function () {
            var location = window.location.host;
            if(location.includes("des-") || location.includes("localhost") || location.includes("ext-"))
              return true;
            else
              return false;
          };

         var ehAmbienteLocal = function () {
            var location = window.location.host;
            if( location.includes("localhost") )
              return true;
            else
              return false;
          };

        return {
            consultarTiposImovelSU: consultarTiposImovelSU,
            consultarNaturezasImovelSU: consultarNaturezasImovelSU,
            consultarImovelPaisesSU: consultarImovelPaisesSU,
            consultarTiposLogradouroSU: consultarTiposLogradouroSU,
            pesquisarUfsIntegrador: pesquisarUfsIntegrador,
            pesquisarMuniciposPorUfIntegrador: pesquisarMuniciposPorUfIntegrador,
            pesquisarLocalidadePorCepIntegrador: pesquisarLocalidadePorCepIntegrador,
            pesquisarCartoriosPorUfIntegrador: pesquisarCartoriosPorUfIntegrador,
            pesquisarCartoriosPorUfMunicipioIntegrador: pesquisarCartoriosPorUfMunicipioIntegrador,
            pesquisarTiposProprietarioRealIncorporacao: pesquisarTiposProprietarioRealIncorporacao,
            pesquisarTiposProprietarioParcialIncorporacao: pesquisarTiposProprietarioParcialIncorporacao,
            pesquisarTiposUtilizacaoDestinacao: pesquisarTiposUtilizacaoDestinacao,
            pesquisarSubTiposUtilizacaoDestinacao: pesquisarSubTiposUtilizacaoDestinacao,
            pesquisarDadosUtilizacoesDestinacao: pesquisarDadosUtilizacoesDestinacao,
            ehAmbienteDesenvolvimento: ehAmbienteDesenvolvimento,
            ehAmbienteLocal: ehAmbienteLocal
        };
    }

})();

(function(){
    'use strict';
    service.$inject = ["$http"];
    angular
        .module('su-avaliacao')
        .factory('documentoSeiService', service);

    function service ($http) {

        var URL_COMUM = 'avaliacao/api/documento-sei';

        var recuperarDocumentoSei = function (num, uf) {
            return $http.get(URL_COMUM + '/recuperar-documento-sei/' + num + '/' + uf);
        };

        var recuperarProcedimentoSei = function (num, uf) {
            return $http.get(URL_COMUM + '/recuperar-procedimento-sei/' + num + '/' + uf);
        };

        return {
            recuperarDocumentoSei: recuperarDocumentoSei,
            recuperarProcedimentoSei: recuperarProcedimentoSei

        };
    }

})();
(function () {
    'use strict';
    angular.module('su-avaliacao').factory('documentoCartorialService', ["$http", function ($http) {

        var URL_ARQUIVO = '/avaliacao/api/documento-cartorial';

        var deletarDocumentoCartorial = function (idArquivo) {
            return $http.delete(URL_ARQUIVO + '/' + idArquivo);
        };

        return {
            deletarDocumentoCartorial: deletarDocumentoCartorial
        };

    }]);
})();

(function () {

    'use strict';

    angular
        .module('su-avaliacao')
        .factory('contadorRomanoService', service);

    function service () {

        var counter = 1;

        var orderedList = [];

        function compare(one, other){
            if(one.order > other.order){
                return 1;
            }
            if(one.order < other.order){
                return -1;
            }
            return 0;
        }

        function convertAll(list) {
            for(var i = 0; i < list.length; i++){
                list[i].number.value = toRoman(i+1);
            }
        }

        function orderCount(number, order, parent){
            var list = orderedList;
            if(parent){
                if(angular.isUndefined(parent.list)){
                    parent.list = [];
                }
                list = parent.list;
            }
            var item = {
                number: number,
                order: order
            };
            if(contains(list, item)){
                return;
            }
            list.push(item);
            list.sort(compare);
            convertAll(list);
        }

        function contains(list, element){
            for(var i = 0; i < list.length; i++){
                if(list[i].number == element.number){
                    return true;
                }
            }
            return false;
        }

        function toRoman(num) {
            if (!+num)
                return false;
            var digits = String(+num).split(""),
                key = ["","C","CC","CCC","CD","D","DC","DCC","DCCC","CM",
                    "","X","XX","XXX","XL","L","LX","LXX","LXXX","XC",
                    "","I","II","III","IV","V","VI","VII","VIII","IX"],
                roman = "",
                i = 3;
            while (i--)
                roman = (key[+digits.pop() + (i * 10)] || "") + roman;
            return Array(+digits.join("") + 1).join("M") + roman;
        }

        function count(){
            return toRoman(counter++);
        }

        function reset(){
            orderedList = [];
            counter = 0;
        }

        return {
            count: count,
            reset: reset,
            toRoman: toRoman,
            orderCount: orderCount
        };
    }

})();

(function(){
    'use strict';
    service.$inject = ["$http"];
    angular
        .module('su-avaliacao')
        .factory('comumAvaliacaoService', service);

    function service ($http) {

        var URL_COMUM = 'avaliacao/api/comum';

        var buscarUrlsMenusPorAmbiente = function () {
            return $http.get('avaliacao/api/comum/url-menu');
        };

        var buscarSituacoesAlteracaoAvaliacao = function() {
            return $http.get(URL_COMUM + '/buscar/motivo/alteracao/avaliacao');
        };

        var buscarTiposUnidade = function() {
            return $http.get(URL_COMUM + '/buscar/tipo/unidade');
        };

        var consultarStatusAvaliacao = function() {
            return $http.get(URL_COMUM + '/buscar/status-avaliacao/all');
        };

        var recuperarData = function(string) {
            var data = new Date(string);
            data.setDate(data.getDate());
            return data;
        };

        var consultarMotivosCancelar = function () {
            return $http.get(URL_COMUM + '/buscar/motivo/cancelamento');
        };

        var usuarioLogado = function () {
            return $http.get(URL_COMUM + '/buscar/usuario/logado');
        };

        var buscarPadroesAcabamento = function() {
            return $http.get(URL_COMUM + '/buscar/padrao/acabamento');
        };

        var buscarTiposProjetosCub = function() {
            return $http.get(URL_COMUM + '/buscar/tipo-projeto/cub');
        };

        var converterJsonToGeo = function(geometris) {
            var geoJson = new ol.format.GeoJSON({
                defaultProjection: 'EPSG:4674'
            });
            return geoJson.readGeometry(geometris);
        };

        var buscarPessoaFisica = function (cpf) {
            return $http.get(URL_COMUM + '/buscar/pessoa/integrador/' + cpf);
        };

        var atualizarPessoaFisica = function (pessoa) {
            return $http.post(URL_COMUM + '/atualizar/pessoa/integrador', pessoa);
        };

        var buscarHistoricoPessoaFisica = function(campo, cpf) {
            return $http.get(URL_COMUM + '/pessoa/fisica/historico?campo=' + campo + '&cpf=' + cpf);
        }


        var buscarPessoaJuridica = function (cnpj) {
            return $http.get(URL_COMUM + '/buscar/pessoa-juridica/integrador/' + cnpj);
        };

        var buscarHistoricoPessoaJuridica = function(campo, cnpj) {
            return $http.get(URL_COMUM + '/pessoa/juridica/historico?campo=' + campo + '&cnpj=' + cnpj);
        };

        var buscarUrlGeoServer = function(){
            return $http.get('avaliacao/api/comum/buscar-url-geoserver');
        };

        var buscarUrlGeoWebCache = function(){
            return $http.get('avaliacao/api/comum/buscar-url-geowebcache');
        };

        var buscarDataAtual = function() {
            return $http.get(URL_COMUM + '/data-atual');
        }

        return {
            buscarUrlsMenusPorAmbiente: buscarUrlsMenusPorAmbiente,
            buscarSituacoesAlteracaoAvaliacao: buscarSituacoesAlteracaoAvaliacao,
            consultarStatusAvaliacao: consultarStatusAvaliacao,
            buscarTiposUnidade: buscarTiposUnidade,
            recuperarData: recuperarData,
            consultarMotivosCancelar : consultarMotivosCancelar,
            usuarioLogado: usuarioLogado,
            buscarPadroesAcabamento: buscarPadroesAcabamento,
            buscarTiposProjetosCub: buscarTiposProjetosCub,
            converterJsonToGeo: converterJsonToGeo,
            buscarPessoaFisica: buscarPessoaFisica,
            buscarHistoricoPessoaFisica: buscarHistoricoPessoaFisica,
            buscarPessoaJuridica: buscarPessoaJuridica,
            buscarHistoricoPessoaJuridica: buscarHistoricoPessoaJuridica,
            buscarUrlGeoServer : buscarUrlGeoServer,
            buscarUrlGeoWebCache : buscarUrlGeoWebCache,
            atualizarPessoaFisica : atualizarPessoaFisica,
            buscarDataAtual: buscarDataAtual

        };
    }

})();
// (function () {
//     'use strict';
//     /*global angular*/
//     /*global ol*/
//     angular.module('ciMapaDirective').factory('ciMapaValidacaoFactory', function () {

//         var geoJson = {};

//         var vm = this,
//         validaCepPontoMunicipio;

//         validaCepPontoMunicipio = function (geomMunicipio, coordPont){

//             geoJson.type = 'FeatureCollection';
//             geoJson.crs = {
//                       'type': 'name',
//                       'properties': {
//                         'name': 'EPSG:3857'
//                       }};

//             var response;
//             if(geomMunicipio.coordinates != undefined){
//                 geoJson.features = [{
//                     'type': 'Feature',
//                     'geometry': {"type":"MultiPolygon",
//                     'coordinates': geomMunicipio.coordinates}
//                 }];

//                 var feature = (new ol.format.GeoJSON()).readFeatures(geoJson);
//                 var geometria = feature[0].getGeometry();
//                 if(geometria.intersectsCoordinate(coordPont)){
//                     response = true;
//                 }else{
//                     response = false;
//                 }
//             }
//             return response;
//         }

//         return {
//             validaCepPontoMunicipio: validaCepPontoMunicipio
//         }

//         criaPontoPorCoordenada = function(coordenadas){
//             return ponto;
//         }

//     });
// }());

(function () {
    "use strict";
    angular.module("su-avaliacao")
        .factory('CartografiaService', ["$http", "urlBase", "UfsService", function ($http, urlBase, UfsService) {
            function transformarGeometria(sistemaReferenciaEntrada, sistemaReferenciaSaida, coordenadaX, coordenadaY) {
                var parametros = {
                    sistemaReferenciaEntrada: sistemaReferenciaEntrada,
                    sistemaReferenciaSaida: sistemaReferenciaSaida,
                    geometriaPonto: {
                        type: 'Point',
                        coordinates: [
                            coordenadaX,
                            coordenadaY
                        ]
                    }
                };
                return $http.post(urlBase + 'cartografia/geometria/transforma', parametros);
            }

            function gdToUtm(gd) {
                var lat = parseFloat(gd.latitude.decimal);
                var lng = parseFloat(gd.longitude.decimal);
                var k0 = 0.9996; //scale on central meridian
                var a = 6378137.0; //equatorial radius, meters.
                var f = 0.00335281066; //polar flattening.
                var b = a*(1-f); //polar axis.
                var e = Math.sqrt(1 - (b/a)*(b/a)); //eccentricity
                var drad = Math.PI/180; //Convert degrees to radians

                var phi = lat * drad; //Convert latitude to radians
                var utmz = 1 + Math.floor((lng+180)/6); //calculate utm zone
                var zcm = 3 + 6*(utmz-1) - 180; //Central meridian of zone

                // Calculate Intermediate Terms
                var esq = (1 - (b/a)*(b/a)); //e squared for use in expansions
                var e0sq = e*e/(1-e*e); // e0 squared - always even powers
                var N = a/Math.sqrt(1-Math.pow(e*Math.sin(phi),2));
                var T = Math.pow(Math.tan(phi),2);
                var C = e0sq*Math.pow(Math.cos(phi),2);
                var A = (lng-zcm)*drad*Math.cos(phi);

                // Calculate M
                var M = phi*(1 - esq*(1/4 + esq*(3/64 + 5*esq/256)));
                M = M - Math.sin(2*phi)*(esq*(3/8 + esq*(3/32 + 45*esq/1024)));
                M = M + Math.sin(4*phi)*(esq*esq*(15/256 + esq*45/1024));
                M = M - Math.sin(6*phi)*(esq*esq*esq*(35/3072));
                M = M*a; //Arc length along standard meridian

                // Calculate UTM Values
                var x = (k0*N*A*(1 + A*A*((1-T+C)/6 + A*A*(5 - 18*T + T*T + 72*C -58*e0sq)/120))) + 500000;
                var y = k0*(M + N*Math.tan(phi)*(A*A*(1/2 + A*A*((5 - T + 9*C + 4*C*C)/24 + A*A*(61 - 58*T + T*T + 600*C - 330*e0sq)/720))));

                if (y < 0) {
                    y = 10000000 + y;
                }

                return {
                    coordenadaE: x,
                    coordenadaN: y,
                    fusoUTM: {
                        zona: utmz,
                        hemisferio: phi < 0 ? "S" : "N"
                    }
                };
            }

            function utmToGd(m) {
                var easting = m.coordenadaE;
                var northing = m.coordenadaN;
                var zone = m.fusoUTM.zona;
                var northernHemisphere = m.fusoUTM.hemisferio === "N";

                if (!northernHemisphere){
                    northing = 10000000 - northing;
                }

                var a = 6378137;
                var e = 0.081819191;
                var e1sq = 0.006739497;
                var k0 = 0.9996;

                var arc = northing / k0;
                var mu = arc / (a * (1 - Math.pow(e, 2) / 4.0 - 3 * Math.pow(e, 4) / 64.0 - 5 * Math.pow(e, 6) / 256.0));

                var ei = (1 - Math.pow((1 - e * e), (1 / 2.0))) / (1 + Math.pow((1 - e * e), (1 / 2.0)));

                var ca = 3 * ei / 2 - 27 * Math.pow(ei, 3) / 32.0;

                var cb = 21 * Math.pow(ei, 2) / 16 - 55 * Math.pow(ei, 4) / 32;
                var cc = 151 * Math.pow(ei, 3) / 96;
                var cd = 1097 * Math.pow(ei, 4) / 512;
                var phi1 = mu + ca * Math.sin(2 * mu) + cb * Math.sin(4 * mu) + cc * Math.sin(6 * mu) + cd * Math.sin(8 * mu);

                var n0 = a / Math.pow((1 - Math.pow((e * Math.sin(phi1)), 2)), (1 / 2.0));

                var r0 = a * (1 - e * e) / Math.pow((1 - Math.pow((e * Math.sin(phi1)), 2)), (3 / 2.0));
                var fact1 = n0 * Math.tan(phi1) / r0;

                var _a1 = 500000 - easting;
                var dd0 = _a1 / (n0 * k0);
                var fact2 = dd0 * dd0 / 2;

                var t0 = Math.pow(Math.tan(phi1), 2);
                var Q0 = e1sq * Math.pow(Math.cos(phi1), 2);
                var fact3 = (5 + 3 * t0 + 10 * Q0 - 4 * Q0 * Q0 - 9 * e1sq) * Math.pow(dd0, 4) / 24;

                var fact4 = (61 + 90 * t0 + 298 * Q0 + 45 * t0 * t0 - 252 * e1sq - 3 * Q0 * Q0) * Math.pow(dd0, 6) / 720;

                var lof1 = _a1 / (n0 * k0);
                var lof2 = (1 + 2 * t0 + Q0) * Math.pow(dd0, 3) / 6.0;
                var lof3 = (5 - 2 * Q0 + 28 * t0 - 3 * Math.pow(Q0, 2) + 8 * e1sq + 24 * Math.pow(t0, 2)) * Math.pow(dd0, 5) / 120;
                var _a2 = (lof1 - lof2 + lof3) / Math.cos(phi1);
                var _a3 = _a2 * 180 / Math.PI;

                var latitude = 180 * (phi1 - fact1 * (fact2 + fact3 + fact4)) / Math.PI;

                if (!northernHemisphere){
                    latitude = -latitude;
                }

                var longitude = ((zone > 0) && (6 * zone - 183.0) || 3.0) - _a3;

                return {
                    latitude: {
                        hemisferio: latitude < 0 ? "S" : "N",
                        decimal: latitude
                    },
                    longitude: {
                        hemisferio: longitude < 0 ? "O" : "L",
                        decimal: longitude
                    }
                };
            }

            function gmsToGd(gms) {
                var latitude = parseFloat(gms.latitude.graus) + parseFloat(gms.latitude.minutos/60 + gms.latitude.segundos/3600);
                var longitude = parseFloat(gms.longitude.graus) + parseFloat(gms.longitude.minutos/60 + gms.longitude.segundos/3600);

                if (gms.latitude.hemisferio === "S") {
                    latitude *= -1;
                }

                if (gms.longitude.hemisferio === "O") {
                    longitude *= -1;
                }

                return {
                    latitude: {
                        hemisferio: gms.latitude.hemisferio,
                        decimal: latitude
                    },
                    longitude: {
                        hemisferio: gms.longitude.hemisferio,
                        decimal: longitude
                    }
                };
            }

            function gdToGms(gd) {
                var obj = {
                    latitude: {
                        hemisferio: null,
                        graus: null,
                        minutos: null,
                        segundos: null
                    },
                    longitude: {
                        hemisferio: null,
                        graus: null,
                        minutos: null,
                        segundos: null
                    }
                };

                var latitude = Math.abs(gd.latitude.decimal);
                var longitude = Math.abs(gd.longitude.decimal);

                obj.latitude.graus = parseInt(latitude);
                obj.latitude.minutos = parseInt((latitude - obj.latitude.graus) * 60);
                obj.latitude.segundos = (latitude - obj.latitude.graus - (obj.latitude.minutos / 60)) * 3600;
                obj.latitude.hemisferio = gd.latitude.hemisferio;

                obj.longitude.graus = parseInt(longitude);
                obj.longitude.minutos = parseInt((longitude - obj.longitude.graus) * 60);
                obj.longitude.segundos = (longitude - obj.longitude.graus - (obj.longitude.minutos / 60)) * 3600;
                obj.longitude.hemisferio = gd.longitude.hemisferio;

                return obj;
            }

            function buscarGeometria(endereco) {
                return UfsService.RecuperaUF.query({sigla: endereco.uf}).$promise.then(
                    function (result) {
                        var enderecamentoImovel = {
                            uf: result[0].descricao,
                            municipio: endereco.municipio,
                            bairro: endereco.bairro
                        };

                        return $http.post(urlBase + "cartografia/geometria/por-endereco", enderecamentoImovel);
                    }
                );
            }

            return {
                transformarGeometria: transformarGeometria,
                buscarGeometria: buscarGeometria,
                utmToGd: utmToGd,
                gdToUtm: gdToUtm,
                gmsToGd: gmsToGd,
                gdToGms: gdToGms
            };
        }])
        .factory('SistemaReferenciaService', ["$http", "urlBase", function ($http, urlBase) {
            function buscarTodos() {
                return $http.get(urlBase + 'dominio/sistemas-referencia');
            }

            return {
                buscarTodos: buscarTodos
            };
        }])
        .factory('FusoUTMService', ["$http", "urlBase", function ($http, urlBase) {
            function buscarTodos() {
                return $http.get(urlBase + 'dominio/fusos-utm');
            }

            return {
                buscarTodos: buscarTodos
            };
        }]);
})();

(function () {

    'use strict';

    angular
        .module('su-avaliacao')
        .factory('avaliacaoEscopoCompartilhadoService', service);

    function service () {
        var _objetos = {};

        var _urlsPorAmbiente = {};

        function setObjetos (nomeObjeto, objeto) {
            _objetos[nomeObjeto] = objeto;
        }

        function getObjeto(nomeObjeto) {
            return _objetos[nomeObjeto];
        }

        function setUrlsPorAmbiente(urls) {
            return _urlsPorAmbiente = urls;
        }

        function getUrlsPorAmbiente() {
            return _urlsPorAmbiente;
        }

        function limparEscopo(){
            _objetos = {};
        }

        return {
            setObjetos: setObjetos,
            getObjeto: getObjeto,
            setUrlsPorAmbiente: setUrlsPorAmbiente,
            getUrlsPorAmbiente: getUrlsPorAmbiente,
            limparEscopo: limparEscopo
        };
    }

})();

(function () {
    'use strict';
angular
    .module('su-avaliacao')
    .factory('arquivoAvaliacaoService', ["$http", "Upload", function ($http, Upload) {

        var URL_ARQUIVO = '/avaliacao/api/arquivo';

        var uploadArquivo = function($file){
            return Upload.upload({
                url: URL_ARQUIVO + '/upload',
                data: {
                    file: $file
                }
            });
        };

        var downloadArquivo = function(idArquivo) {
            return URL_ARQUIVO + '/download/' + idArquivo;
        };

        var deletarArquivo = function(idArquivo) {
            return $http.delete(URL_ARQUIVO + '/' + idArquivo);
        };

        var convertStringToPdf = function($file, contentType){
            var bytes=stringToArray($file);
            var file =new Blob([bytes], {type: contentType});
            var fileURL = URL.createObjectURL(file);
            window.open(fileURL);
        };

        return {
            uploadArquivo: uploadArquivo,
            convertStringToPdf: convertStringToPdf,
            downloadArquivo: downloadArquivo,
            deletarArquivo: deletarArquivo
        };

    }]);
})();

(function () {
    'use strict';
    angular.module('su-avaliacao')
        .factory('errorHandlerInterceptorAvaliacao', ["$q", "$window", "$cookies", "$cookieStore", "$state", "urlPrefixo", function ($q, $window, $cookies, $cookieStore,  $state, urlPrefixo) {
            return {
                'responseError': function (response) {
                    if (response.status == 503){
                        console.log('Servico Indisponivel');
                        $state.go('avaliacao.SERVICO_INDISPONIVEL');
                        return $q(function () { return null; })
                    }

                    else if ((response.status == 403 && response.data.path.startsWith(urlPrefixo)) || (response.status == 500 && response.data.message.startsWith('Cannot call sendError()')) ) {
                        console.log('Redirect Login...')

                        angular.forEach($cookies, function (v, k) {
                            $cookieStore.remove(k);
                        });
                        
                        $window.location.href = urlPrefixo + '/api';
                    }
                    return $q.reject(response);
                }
            };
        }]);
})();

/**
 * Created by Basis Tecnologia on 15/06/2016.
 */
(function () {
    'use strict';

    angular.module('su-avaliacao').filter('PagoNaoPago', function () {
        return function (input) {
            if (input)
                return input ? 'Pago' : 'Não Pago';
        }
    });

    angular.module('su-avaliacao').filter('rip', function () {
        return function (input) {
            if (!input)
                return;
            return input.toString().substr(0, 4) + '.' + input.toString().substr(4, 7) + '-' + input.toString().substr(11, 2);
        };
    });

    angular.module('su-avaliacao').filter('cep', function () {
        return function (input) {
            if (!input)
                return;
            return input.toString().substr(0, 5) + '-' + input.toString().substr(5, 3);
        };
    });

    angular.module('su-avaliacao').filter('ripSpiuNet', function () {
        return function (input) {
            if (!input)
                return;

            if(input.toString().length != 13){
                return input.toString();
            }
            return input.toString().substr(0, 4) + ' ' + input.toString().substr(4, 5)
            + '.' + input.toString().substr(9, 3) + '-' + input.toString().substr(12, 1);
        };
    });

    angular.module("su-avaliacao").filter('cpfcnpj', function () {
        return function (input) {
            if (!input) {
                return;
            } else if (input.toString().length <= 11 && input.toString().length > 3) {
                while (input.toString().length < 11) {
                    input = '0' + input
                }
                return input.toString().substr(0, 3) + '.' + input.toString().substr(3, 3) + '.' + input.toString().substr(6, 3) + '-' + input.toString().substr(9, 2);
            }
            else {
                while (input.toString().length < 14) {
                    input = '0' + input
                }
                return input.toString().substr(0, 2) + '.' + input.toString().substr(2, 3) + '.' + input.toString().substr(5, 3) + '/' + input.toString().substr(8, 4) + '-' + input.toString().substr(12, 2);
            }
        };
    });

    angular.module('su-avaliacao').filter('telefonebr', function () {
        return function (input) {
            if (!input) {
                return 'Não informado'
            }
            if (input.toString().length == 11) {
                return '(' + input.toString().substr(0, 2) + ') ' + input.toString().substr(2, 5) + '-' + input.toString().substr(7, 4);
            } else if (input.toString().length == 10) {
                return '(' + input.toString().substr(0, 2) + ') ' + input.toString().substr(2, 4) + '-' + input.toString().substr(6, 4);
            }
        };
    });


    angular.module('su-avaliacao').filter('dataformatada', function () {
        return function (input) {
            if (!input) {
                return;
            }

            var data = new Date(input);
            data.setDate(data.getDate());

            var dia = data.getDate();
            if (dia.toString().length == 1)
                dia = "0" + dia;
            var mes = data.getMonth() + 1;
            if (mes.toString().length == 1)
                mes = "0" + mes;
            var ano = data.getFullYear();
            return dia + "/" + mes + "/" + ano;
        };
    });

    angular.module('su-avaliacao').filter('simNao', function () {
        return function (input) {
            return input ? 'Sim' : 'Não';
        };
    });


    angular.module('su-avaliacao')
        .filter('to_trusted', ['$sce', function ($sce) {
            return function (text) {
                return $sce.trustAsHtml(text);
            };
        }]);


    angular.module('su-avaliacao').filter('startFrom', function () {
        return function (input, start) {
            if (input) {
                start = +start;
                return input.slice(start);
            }
            return [];
        };
    });

    angular.module("su-avaliacao").filter('processoSei', function () {
        return function (input) {
            if (!input) {
                return;
            }
            else {
                return input.toString().substr(0, 7) + '.' + input.toString().substr(7, 6) + '/' + input.toString().substr(13, 4) + '-' + input.toString().substr(17, 2);
            }
        };
    });

    angular.module("su-avaliacao").filter("addMonths", function() {
        return function(input, months) {
            if (!input) return '';
            var date = new Date(input);
            if (isNaN(date.getTime())) {
                return '';
            }

            date.setMonth(date.getMonth() + months);
            return date;
        };
    });


})();

/*
 * Angular Material Data Table
 * https://github.com/daniel-nagy/md-data-table
 * @license MIT
 * v0.10.4

 * NÃO ALTERAR OU APAGAR ESTE ARQUIVO.
 * NECESSÁRIO PARA TRADUZIR OS COMPONENTES DE PAGINAÇÃO
 * @RAPHAEL.SOUZA
*/

(function (window, angular, undefined) {
'use strict';

angular.module('md.table.templates', ['md-table-pagination.html', 'md-table-progress.html', 'arrow-up.svg', 'navigate-before.svg', 'navigate-first.svg', 'navigate-last.svg', 'navigate-next.svg']);

angular.module('md-table-pagination.html', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('md-table-pagination.html',
    '<div class="page-select" ng-if="$pagination.showPageSelect()">\n' +
    '  <div class="label">{{$pagination.label.page}}</div>\n' +
    '\n' +
    '  <md-select virtual-page-select total="{{$pagination.pages()}}" class="md-table-select" ng-model="$pagination.page" md-container-class="md-pagination-select" ng-change="$pagination.onPaginationChange()" ng-disabled="$pagination.disabled" aria-label="Page">\n' +
    '    <md-content>\n' +
    '      <md-option ng-repeat="page in $pageSelect.pages" ng-value="page">{{page}}</md-option>\n' +
    '    </md-content>\n' +
    '  </md-select>\n' +
    '</div>\n' +
    '\n' +
    '<div class="limit-select" ng-if="$pagination.limitOptions">\n' +
    '  <div class="label">{{$pagination.label.rowsPerPage}}</div>\n' +
    '\n' +
    '  <md-select class="md-table-select" ng-model="$pagination.limit" md-container-class="md-pagination-select" ng-disabled="$pagination.disabled" aria-label="Rows" placeholder="{{ $pagination.limitOptions[0] }}">\n' +
    '    <md-option ng-repeat="rows in $pagination.limitOptions" ng-value="rows">{{rows}}</md-option>\n' +
    '  </md-select>\n' +
    '</div>\n' +
    '\n' +
    '<div class="buttons">\n' +
    '  <div class="label">{{$pagination.min()}} - {{$pagination.max()}} {{$pagination.label.of}} {{$pagination.total}}</div>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-if="$pagination.showBoundaryLinks()" ng-click="$pagination.first()" ng-disabled="$pagination.disabled || !$pagination.hasPrevious()" aria-label="First">\n' +
    '    <md-icon md-svg-icon="navigate-first.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-click="$pagination.previous()" ng-disabled="$pagination.disabled || !$pagination.hasPrevious()" aria-label="Previous">\n' +
    '    <md-icon md-svg-icon="navigate-before.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-click="$pagination.next()" ng-disabled="$pagination.disabled || !$pagination.hasNext()" aria-label="Next">\n' +
    '    <md-icon md-svg-icon="navigate-next.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '\n' +
    '  <md-button class="md-icon-button" type="button" ng-if="$pagination.showBoundaryLinks()" ng-click="$pagination.last()" ng-disabled="$pagination.disabled || !$pagination.hasNext()" aria-label="Last">\n' +
    '    <md-icon md-svg-icon="navigate-last.svg"></md-icon>\n' +
    '  </md-button>\n' +
    '</div>');
}]);

angular.module('md-table-progress.html', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('md-table-progress.html',
    '<tr>\n' +
    '  <th colspan="{{columnCount()}}">\n' +
    '    <md-progress-linear ng-show="deferred()" md-mode="indeterminate"></md-progress-linear>\n' +
    '  </th>\n' +
    '</tr>');
}]);

angular.module('arrow-up.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('arrow-up.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"/></svg>');
}]);

angular.module('navigate-before.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-before.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>');
}]);

angular.module('navigate-first.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-first.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M7 6 v12 h2 v-12 h-2z M17.41 7.41L16 6l-6 6 6 6 1.41-1.41L12.83 12z"/></svg>');
}]);

angular.module('navigate-last.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-last.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M15 6 v12 h2 v-12 h-2z M8 6L6.59 7.41 11.17 12l-4.58 4.59L8 18l6-6z"/></svg>');
}]);

angular.module('navigate-next.svg', []).run(['$templateCache', function($templateCache) {
  $templateCache.put('navigate-next.svg',
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>');
}]);


angular.module('md.data.table', ['md.table.templates']);

angular.module('md.data.table').directive('mdBody', mdBody);

function mdBody() {

  function compile(tElement) {
    tElement.addClass('md-body');
  }

  return {
    compile: compile,
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdCell', mdCell);

function mdCell() {
  
  function compile(tElement) {
    var select = tElement.find('md-select');
    
    if(select.length) {
      select.addClass('md-table-select').attr('md-container-class', 'md-table-select');
    }
    
    tElement.addClass('md-cell');
    
    return postLink;
  }
  
  // empty controller to be bind properties to in postLink function
  function Controller() {
    
  }
  
  function postLink(scope, element, attrs, ctrls) {
    var select = element.find('md-select');
    var cellCtrl = ctrls.shift();
    var tableCtrl = ctrls.shift();
    
    if(attrs.ngClick) {
      element.addClass('md-clickable');
    }
    
    if(select.length) {
      select.on('click', function (event) {
        event.stopPropagation();
      });
      
      element.addClass('md-clickable').on('click', function (event) {
        event.stopPropagation();
        select[0].click();
      });
    }
    
    cellCtrl.getTable = tableCtrl.getElement;
    
    function getColumn() {
      return tableCtrl.$$columns[getIndex()];
    }
    
    function getIndex() {
      return Array.prototype.indexOf.call(element.parent().children(), element[0]);
    }
    
    scope.$watch(getColumn, function (column) {
      if(!column) {
        return;
      }
      
      if(column.numeric) {
        element.addClass('md-numeric');
      } else {
        element.removeClass('md-numeric');
      }
    });
  }
  
  return {
    controller: Controller,
    compile: compile,
    require: ['mdCell', '^^mdTable'],
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdColumn', mdColumn);

function mdColumn($compile, $mdUtil) {
  
  function compile(tElement) {
    tElement.addClass('md-column');
    return postLink;
  }

  function postLink(scope, element, attrs, ctrls) {
    var headCtrl = ctrls.shift();
    var tableCtrl = ctrls.shift();
    
    function attachSortIcon() {
      var sortIcon = angular.element('<md-icon md-svg-icon="arrow-up.svg">');
      
      $compile(sortIcon.addClass('md-sort-icon').attr('ng-class', 'getDirection()'))(scope);
      
      if(element.hasClass('md-numeric')) {
        element.prepend(sortIcon);
      } else {
        element.append(sortIcon);
      }
    }
    
    function detachSortIcon() {
      Array.prototype.some.call(element.find('md-icon'), function (icon) {
        return icon.classList.contains('md-sort-icon') && element[0].removeChild(icon);
      });
    }
    
    function disableSorting() {
      detachSortIcon();
      element.removeClass('md-sort').off('click', setOrder);
    }
    
    function enableSorting() {
      attachSortIcon();
      element.addClass('md-sort').on('click', setOrder);
    }
    
    function getIndex() {
      return Array.prototype.indexOf.call(element.parent().children(), element[0]);
    }
    
    function isActive() {
      return scope.orderBy && (headCtrl.order === scope.orderBy || headCtrl.order === '-' + scope.orderBy);
    }
    
    function isNumeric() {
      return attrs.mdNumeric === '' || scope.numeric;
    }
    
    function setOrder() {
      scope.$applyAsync(function () {
        if(isActive()) {
          headCtrl.order = scope.getDirection() === 'md-asc' ? '-' + scope.orderBy : scope.orderBy;
        } else {
          headCtrl.order = scope.getDirection() === 'md-asc' ? scope.orderBy : '-' + scope.orderBy;
        }
        
        if(angular.isFunction(headCtrl.onReorder)) {
          $mdUtil.nextTick(function () {
            headCtrl.onReorder(headCtrl.order);
          });
        }
      });
    }
    
    function updateColumn(index, column) {
      tableCtrl.$$columns[index] = column;
      
      if(column.numeric) {
        element.addClass('md-numeric');
      } else {
        element.removeClass('md-numeric');
      }
    }
    
    scope.getDirection = function () {
      if(isActive()) {
        return headCtrl.order.charAt(0) === '-' ? 'md-desc' : 'md-asc';
      }
      
      return attrs.mdDesc === '' || scope.$eval(attrs.mdDesc) ? 'md-desc' : 'md-asc';
    };
    
    scope.$watch(isActive, function (active) {
      if(active) {
        element.addClass('md-active');
      } else {
        element.removeClass('md-active');
      }
    });
    
    scope.$watch(getIndex, function (index) {
      updateColumn(index, {'numeric': isNumeric()});
    });
    
    scope.$watch(isNumeric, function (numeric) {
      updateColumn(getIndex(), {'numeric': numeric});
    });
    
    scope.$watch('orderBy', function (orderBy) {
      if(orderBy) {
        enableSorting();
      } else {
        disableSorting();
      }
    });
  }

  return {
    compile: compile,
    require: ['^^mdHead', '^^mdTable'],
    restrict: 'A',
    scope: {
      numeric: '=?mdNumeric',
      orderBy: '@?mdOrderBy'
    }
  };
}

mdColumn.$inject = ['$compile', '$mdUtil'];

angular.module('md.data.table')
  .decorator('$controller', controllerDecorator)
  .factory('$mdEditDialog', mdEditDialog);

/*
 * A decorator for ng.$controller to optionally bind properties to the
 * controller before invoking the constructor. Stolen from the ngMock.
 *
 * https://docs.angularjs.org/api/ngMock/service/$controller
 */
function controllerDecorator($delegate) {
  return function(expression, locals, later, ident) {
    if(later && typeof later === 'object') {
      var create = $delegate(expression, locals, true, ident);
      angular.extend(create.instance, later);
      return create();
    }
    return $delegate(expression, locals, later, ident);
  };
}

controllerDecorator.$inject = ['$delegate'];
  
function mdEditDialog($compile, $controller, $document, $mdUtil, $q, $rootScope, $templateCache, $templateRequest, $window) {
  /* jshint validthis: true */
  
  var ESCAPE = 27;
  
  var busy = false;
  var body = angular.element($document.prop('body'));
  
  /*
   * bindToController
   * controller
   * controllerAs
   * locals
   * resolve
   * scope
   * targetEvent
   * template
   * templateUrl
   */
  var defaultOptions = {
    clickOutsideToClose: true,
    disableScroll: true,
    escToClose: true,
    focusOnOpen: true
  };
  
  function build(template, options) {
    var scope = $rootScope.$new();
    var element = $compile(template)(scope);
    var backdrop = $mdUtil.createBackdrop(scope, 'md-edit-dialog-backdrop');
    var controller;
    
    if(options.controller) {
      controller = getController(options, scope, {$element: element, $scope: scope});
    } else {
      angular.extend(scope, options.scope);
    }
    
    if(options.disableScroll) {
      disableScroll(element);
    }
    
    body.prepend(backdrop).append(element.addClass('md-whiteframe-1dp'));
    
    positionDialog(element, options.target);
    
    if(options.focusOnOpen) {
      focusOnOpen(element);
    }
    
    if(options.clickOutsideToClose) {
      backdrop.on('click', function () {
        element.remove();
      });
    }
    
    if(options.escToClose) {
      escToClose(element);
    }
    
    element.on('$destroy', function () {
      busy = false;
      backdrop.remove();
    });
    
    return controller;
  }
  
  function disableScroll(element) {
    var restoreScroll = $mdUtil.disableScrollAround(element, body);
    
    element.on('$destroy', function () {
      restoreScroll();
    });
  }
  
  function getController(options, scope, inject) {
    if(!options.controller) {
      return;
    }
    
    if(options.resolve) {
      angular.extend(inject, options.resolve);
    }
    
    if(options.locals) {
      angular.extend(inject, options.locals);
    }
    
    if(options.controllerAs) {
      scope[options.controllerAs] = {};
      
      if(options.bindToController) {
        angular.extend(scope[options.controllerAs], options.scope);
      } else {
        angular.extend(scope, options.scope);
      }
    } else {
      angular.extend(scope, options.scope);
    }
    
    if(options.bindToController) {
      return $controller(options.controller, inject, scope[options.controllerAs]);
    } else {
      return $controller(options.controller, inject);
    }
  }
  
  function getTemplate(options) {
    return $q(function (resolve, reject) {
      var template = options.template;
      
      function illegalType(type) {
        reject('Unexpected template value. Expected a string; received a ' + type + '.');
      }
      
      if(template) {
        return angular.isString(template) ? resolve(template) : illegalType(typeof template);
      }
      
      if(options.templateUrl) {
        template = $templateCache.get(options.templateUrl);
        
        if(template) {
          return resolve(template);
        }
        
        var success = function (template) {
          return resolve(template);
        };
        
        var error = function () {
          return reject('Error retrieving template from URL.');
        };
        
        return $templateRequest(options.templateUrl).then(success, error);
      }
      
      reject('Template not provided.');
    });
  }
  
  function logError(error) {
    busy = false;
    console.error(error);
  }
  
  function escToClose(element) {
    var keyup = function (event) {
      if(event.keyCode === ESCAPE) {
        element.remove();
      }
    };
    
    body.on('keyup', keyup);
    
    element.on('$destroy', function () {
      body.off('keyup', keyup);
    });
  }

  function focusOnOpen(element) {
    $mdUtil.nextTick(function () {
      var autofocus = $mdUtil.findFocusTarget(element);
      
      if(autofocus) {
        autofocus.focus();
      }
    }, false);
  }

  function positionDialog(element, target) {
    var table = angular.element(target).controller('mdCell').getTable();
    
    var getHeight = function () {
      return element.prop('clientHeight');
    };
    
    var getSize = function () {
      return {
        width: getWidth(),
        height: getHeight()
      };
    };
    
    var getTableBounds = function () {
      var parent = table.parent();
      
      if(parent.prop('tagName') === 'MD-TABLE-CONTAINER') {
        return parent[0].getBoundingClientRect();
      } else {
        return table[0].getBoundingClientRect();
      }
    };
    
    var getWidth = function () {
      return element.prop('clientWidth');
    };
    
    var reposition = function () {
      var size = getSize();
      var cellBounds = target.getBoundingClientRect();
      var tableBounds = getTableBounds();
      
      if(size.width > tableBounds.right - cellBounds.left) {
        element.css('left', tableBounds.right - size.width + 'px');
      } else {
        element.css('left', cellBounds.left + 'px');
      }
      
      if(size.height > tableBounds.bottom - cellBounds.top) {
        element.css('top', tableBounds.bottom - size.height + 'px');
      } else {
        element.css('top', cellBounds.top + 1 + 'px');
      }
      
      element.css('minWidth', cellBounds.width + 'px');
    };
    
    var watchWidth = $rootScope.$watch(getWidth, reposition);
    var watchHeight = $rootScope.$watch(getHeight, reposition);
    
    $window.addEventListener('resize', reposition);
    
    element.on('$destroy', function () {
      watchWidth();
      watchHeight();
      
      $window.removeEventListener('resize', reposition);
    });
  }
  
  function preset(size, options) {
    
    function getAttrs() {
      var attrs = 'type="' + (options.type || 'text') + '"';
      
      for(var attr in options.validators) {
        attrs += ' ' + attr + '="' + options.validators[attr] + '"';
      }
      
      return attrs;
    }
    
    return {
      controller: ['$element', '$q', 'save', '$scope', function ($element, $q, save, $scope) {
        function update() {
          if($scope.editDialog.$invalid) {
            return $q.reject();
          }
          
          if(angular.isFunction(save)) {
            return $q.when(save($scope.editDialog.input));
          }
          
          return $q.resolve();
        }
        
        this.dismiss = function () {
          $element.remove();
        };
        
        this.getInput = function () {
          return $scope.editDialog.input;
        };
        
        $scope.dismiss = this.dismiss;
        
        $scope.submit = function () {
          update().then(function () {
            $scope.dismiss();
          });
        };
      }],
      locals: {
        save: options.save
      },
      scope: {
        cancel: options.cancel || 'Cancel',
        messages: options.messages,
        model: options.modelValue,
        ok: options.ok || 'Save',
        placeholder: options.placeholder,
        title: options.title,
        size: size
      },
      template:
        '<md-edit-dialog>' +
          '<div layout="column" class="md-content">' +
            '<div ng-if="size === \'large\'" class="md-title">{{title || \'Edit\'}}</div>' +
            '<form name="editDialog" layout="column" ng-submit="submit(model)">' +
              '<md-input-container md-no-float>' +
                '<input name="input" ng-model="model" md-autofocus placeholder="{{placeholder}} "' + getAttrs() + '/>' +
                '<div ng-messages="editDialog.input.$error">' +
                  '<div ng-repeat="(key, message) in messages" ng-message="{{key}}">{{message}}</div>' +
                '</div>' +
              '</md-input-container>' +
            '</form>' +
          '</div>' +
          '<div ng-if="size === \'large\'" layout="row" layout-align="end" class="md-actions">' +
            '<md-button class="md-primary" ng-click="dismiss()">{{cancel}}</md-button>' +
            '<md-button class="md-primary" ng-click="submit()">{{ok}}</md-button>' +
          '</div>' +
        '</md-edit-dialog>'
    };
  }
  
  this.show = function (options) {
    if(busy) {
      return $q.reject();
    }
    
    busy = true;
    options = angular.extend({}, defaultOptions, options);
    
    if(!options.targetEvent) {
      return logError('options.targetEvent is required to align the dialog with the table cell.');
    }
    
    if(!options.targetEvent.currentTarget.classList.contains('md-cell')) {
      return logError('The event target must be a table cell.');
    }
    
    if(options.bindToController && !options.controllerAs) {
      return logError('You must define options.controllerAs when options.bindToController is true.');
    }
    
    options.target = options.targetEvent.currentTarget;
    
    var promise = getTemplate(options);
    var promises = [promise];
    
    for(var prop in options.resolve) {
      promise = options.resolve[prop];
      promises.push($q.when(angular.isFunction(promise) ? promise() : promise));
    }
    
    promise = $q.all(promises);
    
    promise['catch'](logError);
    
    return promise.then(function (results) {
      var template = results.shift();
      
      for(var prop in options.resolve) {
        options.resolve[prop] = results.shift();
      }
      
      return build(template, options);
    });
  };
  
  this.small = function (options) {
    return this.show(angular.extend({}, options, preset('small', options)));
  }.bind(this);
  
  this.large = function (options) {
    return this.show(angular.extend({}, options, preset('large', options)));
  }.bind(this);
  
  return this;
}

mdEditDialog.$inject = ['$compile', '$controller', '$document', '$mdUtil', '$q', '$rootScope', '$templateCache', '$templateRequest', '$window'];


angular.module('md.data.table').directive('mdFoot', mdFoot);

function mdFoot() {

  function compile(tElement) {
    tElement.addClass('md-foot');
  }

  return {
    compile: compile,
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdHead', mdHead);

function mdHead($compile) {

  function compile(tElement) {
    tElement.addClass('md-head');
    return postLink;
  }
  
  // empty controller to be bind scope properties to
  function Controller() {
    
  }
  
  function postLink(scope, element, attrs, tableCtrl) {
    // because scope.$watch is unpredictable
    var oldValue = new Array(2);
    
    function addCheckboxColumn() {
      element.children().prepend('<th class="md-column md-checkbox-column">');
    }
    
    function attatchCheckbox() {
      element.prop('lastElementChild').firstElementChild.appendChild($compile(createCheckBox())(scope)[0]);
    }
    
    function createCheckBox() {
      return angular.element('<md-checkbox>').attr({
        'aria-label': 'Select All',
        'ng-click': 'toggleAll()',
        'ng-checked': 'allSelected()',
        'ng-disabled': '!getSelectableRows().length'
      });
    }
    
    function detachCheckbox() {
      var cell = element.prop('lastElementChild').firstElementChild;
      
      if(cell.classList.contains('md-checkbox-column')) {
        angular.element(cell).empty();
      }
    }
    
    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }
    
    function mdSelectCtrl(row) {
      return angular.element(row).controller('mdSelect');
    }
    
    function removeCheckboxColumn() {
      Array.prototype.some.call(element.find('th'), function (cell) {
        return cell.classList.contains('md-checkbox-column') && cell.remove();
      });
    }
    
    scope.allSelected = function () {
      var rows = scope.getSelectableRows();
      
      return rows.length && rows.every(function (row) {
        return row.isSelected();
      });
    };
    
    scope.getSelectableRows = function () {
      return tableCtrl.getBodyRows().map(mdSelectCtrl).filter(function (ctrl) {
        return ctrl && !ctrl.disabled;
      });
    };
    
    scope.selectAll = function () {
      tableCtrl.getBodyRows().map(mdSelectCtrl).forEach(function (ctrl) {
        if(ctrl && !ctrl.isSelected()) {
          ctrl.select();
        }
      });
    };
    
    scope.toggleAll = function () {
      return scope.allSelected() ? scope.unSelectAll() : scope.selectAll();
    };
    
    scope.unSelectAll = function () {
      tableCtrl.getBodyRows().map(mdSelectCtrl).forEach(function (ctrl) {
        if(ctrl && ctrl.isSelected()) {
          ctrl.deselect();
        }
      });
    };
    
    scope.$watchGroup([enableRowSelection, tableCtrl.enableMultiSelect], function (newValue) {
      if(newValue[0] !== oldValue[0]) {
        if(newValue[0]) {
          addCheckboxColumn();
          
          if(newValue[1]) {
            attatchCheckbox();
          }
        } else {
          removeCheckboxColumn();
        }
      } else if(newValue[0] && newValue[1] !== oldValue[1]) {
        if(newValue[1]) {
          attatchCheckbox();
        } else {
          detachCheckbox();
        }
      }
      
      angular.copy(newValue, oldValue);
    });
  }
  
  return {
    bindToController: true,
    compile: compile,
    controller: Controller,
    controllerAs: '$mdHead',
    require: '^^mdTable',
    restrict: 'A',
    scope: {
      order: '=?mdOrder',
      onReorder: '=?mdOnReorder'
    }
  };
}

mdHead.$inject = ['$compile'];

angular.module('md.data.table').directive('mdRow', mdRow);

function mdRow() {

  function compile(tElement) {
    tElement.addClass('md-row');
    return postLink;
  }
  
  function postLink(scope, element, attrs, tableCtrl) {
    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }
    
    function isBodyRow() {
      return tableCtrl.getBodyRows().indexOf(element[0]) !== -1;
    }
    
    function isChild(node) {
      return element[0].contains(node[0]);
    }
    
    if(isBodyRow()) {
      var cell = angular.element('<td class="md-cell">');
      
      scope.$watch(enableRowSelection, function (enable) {
        // if a row is not selectable, prepend an empty cell to it
        if(enable && !attrs.mdSelect) {
          if(!isChild(cell)) {
            element.prepend(cell);
          }
          return;
        }
        
        if(isChild(cell)) {
          cell.remove();
        }
      });
    }
  }

  return {
    compile: compile,
    require: '^^mdTable',
    restrict: 'A'
  };
}

angular.module('md.data.table').directive('mdSelect', mdSelect);

function mdSelect($compile, $parse) {

  // empty controller to bind scope properties to
  function Controller() {

  }

  function postLink(scope, element, attrs, ctrls) {
    var self = ctrls.shift();
    var tableCtrl = ctrls.shift();
    var getId = $parse(attrs.mdSelectId);

    self.id = getId(self.model);

    if(tableCtrl.$$rowSelect && self.id) {
      if(tableCtrl.$$hash.has(self.id)) {
        var index = tableCtrl.selected.indexOf(tableCtrl.$$hash.get(self.id));

        // if the item is no longer selected remove it
        if(index === -1) {
          tableCtrl.$$hash.purge(self.id);
        }

        // if the item is not a reference to the current model update the reference
        else if(!tableCtrl.$$hash.equals(self.id, self.model)) {
          tableCtrl.$$hash.update(self.id, self.model);
          tableCtrl.selected.splice(index, 1, self.model);
        }

      } else {

        // check if the item has been selected
        tableCtrl.selected.some(function (item, index) {
          if(getId(item) === self.id) {
            tableCtrl.$$hash.update(self.id, self.model);
            tableCtrl.selected.splice(index, 1, self.model);

            return true;
          }
        });
      }
    }

    self.isSelected = function () {
      if(!tableCtrl.$$rowSelect) {
        return false;
      }

      if(self.id) {
        return tableCtrl.$$hash.has(self.id);
      }

      return tableCtrl.selected.indexOf(self.model) !== -1;
    };

    self.select = function () {
      if(self.disabled) {
        return;
      }

      if(tableCtrl.enableMultiSelect()) {
        tableCtrl.selected.push(self.model);
      } else {
        tableCtrl.selected.splice(0, tableCtrl.selected.length, self.model);
      }

      if(angular.isFunction(self.onSelect)) {
        self.onSelect(self.model);
      }
    };

    self.deselect = function () {
      if(self.disabled) {
        return;
      }

      tableCtrl.selected.splice(tableCtrl.selected.indexOf(self.model), 1);

      if(angular.isFunction(self.onDeselect)) {
        self.onDeselect(self.model);
      }
    };

    self.toggle = function (event) {
      if(event && event.stopPropagation) {
        event.stopPropagation();
      }

      return self.isSelected() ? self.deselect() : self.select();
    };

    function autoSelect() {
      return attrs.mdAutoSelect === '' || self.autoSelect;
    }

    function createCheckbox() {
      var checkbox = angular.element('<md-checkbox>').attr({
        'aria-label': 'Select Row',
        'ng-click': '$mdSelect.toggle($event)',
        'ng-checked': '$mdSelect.isSelected()',
        'ng-disabled': '$mdSelect.disabled'
      });

      return angular.element('<td class="md-cell md-checkbox-cell">').append($compile(checkbox)(scope));
    }

    function disableSelection() {
      Array.prototype.some.call(element.children(), function (child) {
        return child.classList.contains('md-checkbox-cell') && element[0].removeChild(child);
      });

      if(autoSelect()) {
        element.off('click', toggle);
      }
    }

    function enableSelection() {
      element.prepend(createCheckbox());

      if(autoSelect()) {
        element.on('click', toggle);
      }
    }

    function enableRowSelection() {
      return tableCtrl.$$rowSelect;
    }

    function onSelectChange(selected) {
      if(!self.id) {
        return;
      }

      if(tableCtrl.$$hash.has(self.id)) {
        // check if the item has been deselected
        if(selected.indexOf(tableCtrl.$$hash.get(self.id)) === -1) {
          tableCtrl.$$hash.purge(self.id);
        }

        return;
      }

      // check if the item has been selected
      if(selected.indexOf(self.model) !== -1) {
        tableCtrl.$$hash.update(self.id, self.model);
      }
    }

    function toggle(event) {
      scope.$applyAsync(function () {
        self.toggle(event);
      });
    }

    scope.$watch(enableRowSelection, function (enable) {
      if(enable) {
        enableSelection();
      } else {
        disableSelection();
      }
    });

    scope.$watch(autoSelect, function (newValue, oldValue) {
      if(newValue === oldValue) {
        return;
      }

      if(tableCtrl.$$rowSelect && newValue) {
        element.on('click', toggle);
      } else {
        element.off('click', toggle);
      }
    });

    scope.$watch(self.isSelected, function (isSelected) {
      return isSelected ? element.addClass('md-selected') : element.removeClass('md-selected');
    });

    scope.$watch(tableCtrl.enableMultiSelect, function (multiple) {
      if(tableCtrl.$$rowSelect && !multiple) {
        // remove all but the first selected item
        tableCtrl.selected.splice(1);
      }
    });

    tableCtrl.registerModelChangeListener(onSelectChange);

    element.on('$destroy', function () {
      tableCtrl.removeModelChangeListener(onSelectChange);
    });
  }

  return {
    bindToController: true,
    controller: Controller,
    controllerAs: '$mdSelect',
    link: postLink,
    require: ['mdSelect', '^^mdTable'],
    restrict: 'A',
    scope: {
      model: '=mdSelect',
      disabled: '=ngDisabled',
      onSelect: '=?mdOnSelect',
      onDeselect: '=?mdOnDeselect',
      autoSelect: '=mdAutoSelect'
    }
  };
}

mdSelect.$inject = ['$compile', '$parse'];

angular.module('md.data.table').directive('mdTable', mdTable);

function Hash() {
  var keys = {};
  
  this.equals = function (key, item) {
    return keys[key] === item;
  };

  this.get = function (key) {
    return keys[key];
  };
  
  this.has = function (key) {
    return keys.hasOwnProperty(key);
  };

  this.purge = function (key) {
    delete keys[key];
  };
  
  this.update = function (key, item) {
    keys[key] = item;
  };
}

function mdTable() {
  
  function compile(tElement, tAttrs) {
    tElement.addClass('md-table');
    
    if(tAttrs.hasOwnProperty('mdProgress')) {
      var body = tElement.find('tbody')[0];
      var progress = angular.element('<thead class="md-table-progress">');
      
      if(body) {
        tElement[0].insertBefore(progress[0], body);
      }
    }
  }
  
  function Controller($attrs, $element, $q, $scope) {
    var self = this;
    var queue = [];
    var watchListener;
    var modelChangeListeners = [];
    
    self.$$hash = new Hash();
    self.$$columns = {};
    
    function enableRowSelection() {
      self.$$rowSelect = true;
      
      watchListener = $scope.$watchCollection('$mdTable.selected', function (selected) {
        modelChangeListeners.forEach(function (listener) {
          listener(selected);
        });
      });
      
      $element.addClass('md-row-select');
    }
    
    function disableRowSelection() {
      self.$$rowSelect = false;
      
      if(angular.isFunction(watchListener)) {
        watchListener();
      }
      
      $element.removeClass('md-row-select');
    }
    
    function resolvePromises() {
      if(!queue.length) {
        return $scope.$applyAsync();
      }
      
      queue[0]['finally'](function () {
        queue.shift();
        resolvePromises();
      });
    }
    
    function rowSelect() {
      return $attrs.mdRowSelect === '' || self.rowSelect;
    }
    
    function validateModel() {
      if(!self.selected) {
        return console.error('Row selection: ngModel is not defined.');
      }
      
      if(!angular.isArray(self.selected)) {
        return console.error('Row selection: Expected an array. Recived ' + typeof self.selected + '.');
      }
      
      return true;
    }
    
    self.columnCount = function () {
      return self.getRows($element[0]).reduce(function (count, row) {
        return row.cells.length > count ? row.cells.length : count;
      }, 0);
    };
    
    self.getRows = function (element) {
      return Array.prototype.filter.call(element.rows, function (row) {
        return !row.classList.contains('ng-leave');
      });
    };
    
    self.getBodyRows = function () {
      return Array.prototype.reduce.call($element.prop('tBodies'), function (result, tbody) {
        return result.concat(self.getRows(tbody));
      }, []);
    };
    
    self.getElement = function () {
      return $element;
    };
    
    self.getHeaderRows = function () {
      return self.getRows($element.prop('tHead'));
    };
    
    self.enableMultiSelect = function () {
      return $attrs.multiple === '' || $scope.$eval($attrs.multiple);
    };
    
    self.waitingOnPromise = function () {
      return !!queue.length;
    };
    
    self.queuePromise = function (promise) {
      if(!promise) {
        return;
      }
      
      if(queue.push(angular.isArray(promise) ? $q.all(promise) : $q.when(promise)) === 1) {
        resolvePromises();
      }
    };
    
    self.registerModelChangeListener = function (listener) {
      modelChangeListeners.push(listener);
    };
    
    self.removeModelChangeListener = function (listener) {
      var index = modelChangeListeners.indexOf(listener);
      
      if(index !== -1) {
        modelChangeListeners.splice(index, 1);
      }
    };
    
    if($attrs.hasOwnProperty('mdProgress')) {
      $scope.$watch('$mdTable.progress', self.queuePromise);
    }
    
    $scope.$watch(rowSelect, function (enable) {
      if(enable && !!validateModel()) {
        enableRowSelection();
      } else {
        disableRowSelection();
      }
    });
  }
  
  Controller.$inject = ['$attrs', '$element', '$q', '$scope'];
  
  return {
    bindToController: true,
    compile: compile,
    controller: Controller,
    controllerAs: '$mdTable',
    restrict: 'A',
    scope: {
      progress: '=?mdProgress',
      selected: '=ngModel',
      rowSelect: '=mdRowSelect'
    }
  };
}

angular.module('md.data.table').directive('mdTablePagination', mdTablePagination);

function mdTablePagination() {

  function compile(tElement) {
    tElement.addClass('md-table-pagination');
  }

  function Controller($attrs, $mdUtil, $scope) {
    var self = this;
    var defaultLabel = {
      page: 'Página:',
      rowsPerPage: 'Linhas por página:',
      of: 'de'
    };

    self.label = angular.copy(defaultLabel);

    function isPositive(number) {
      return parseInt(number, 10) > 0;
    }

    self.first = function () {
      self.page = 1;
      self.onPaginationChange();
    };

    self.hasNext = function () {
      return self.page * self.limit < self.total;
    };

    self.hasPrevious = function () {
      return self.page > 1;
    };

    self.last = function () {
      self.page = self.pages();
      self.onPaginationChange();
    };

    self.max = function () {
      return self.hasNext() ? self.page * self.limit : self.total;
    };

    self.min = function () {
      return isPositive(self.total) ? self.page * self.limit - self.limit + 1 : 0;
    };

    self.next = function () {
      self.page++;
      self.onPaginationChange();
    };

    self.onPaginationChange = function () {
      if(angular.isFunction(self.onPaginate)) {
        $mdUtil.nextTick(function () {
          self.onPaginate(self.page, self.limit);
        });
      }
    };

    self.pages = function () {
      return isPositive(self.total) ? Math.ceil(self.total / (isPositive(self.limit) ? self.limit : 1)) : 1;
    };

    self.previous = function () {
      self.page--;
      self.onPaginationChange();
    };

    self.showBoundaryLinks = function () {
      return $attrs.mdBoundaryLinks === '' || self.boundaryLinks;
    };

    self.showPageSelect = function () {
      return $attrs.mdPageSelect === '' || self.pageSelect;
    };

    $scope.$watch('$pagination.limit', function (newValue, oldValue) {
      if(isNaN(newValue) || isNaN(oldValue) || newValue === oldValue) {
        return;
      }

      // find closest page from previous min
      self.page = Math.floor(((self.page * oldValue - oldValue) + newValue) / (isPositive(newValue) ? newValue : 1));
      self.onPaginationChange();
    });

    $attrs.$observe('mdLabel', function (label) {
      angular.extend(self.label, defaultLabel, $scope.$eval(label));
    });

    $scope.$watch('$pagination.total', function (newValue, oldValue) {
      if(isNaN(newValue) || newValue === oldValue) {
        return;
      }

      if(self.page > self.pages()) {
        self.last();
      }
    });
  }

  Controller.$inject = ['$attrs', '$mdUtil', '$scope'];

  return {
    bindToController: {
      boundaryLinks: '=?mdBoundaryLinks',
      disabled: '=ngDisabled',
      limit: '=mdLimit',
      page: '=mdPage',
      pageSelect: '=?mdPageSelect',
      onPaginate: '=?mdOnPaginate',
      limitOptions: '=?mdLimitOptions',
      total: '@mdTotal'
    },
    compile: compile,
    controller: Controller,
    controllerAs: '$pagination',
    restrict: 'E',
    scope: {},
    templateUrl: 'md-table-pagination.html'
  };
}

angular.module('md.data.table').directive('mdTableProgress', mdTableProgress);

function mdTableProgress() {

  function postLink(scope, element, attrs, tableCtrl) {
    scope.columnCount = tableCtrl.columnCount;
    scope.deferred = tableCtrl.waitingOnPromise;
  }

  return {
    link: postLink,
    require: '^^mdTable',
    restrict: 'C',
    scope: {},
    templateUrl: 'md-table-progress.html'
  };
}

angular.module('md.data.table').directive('virtualPageSelect', virtualPageSelect);

function virtualPageSelect() {

  function Controller($element, $scope) {
    var self = this;
    var content = $element.find('md-content');
    
    self.pages = [];
    
    function getMin(pages, total) {
      return Math.min(pages, isFinite(total) && isPositive(total) ? total : 1);
    }
    
    function isPositive(number) {
      return number > 0;
    }
    
    function setPages(max) {
      if(self.pages.length > max) {
        return self.pages.splice(max);
      }
      
      for(var i = self.pages.length; i < max; i++) {
        self.pages.push(i + 1);
      }
    }
    
    content.on('scroll', function () {
      if((content.prop('clientHeight') + content.prop('scrollTop')) >= content.prop('scrollHeight')) {
        $scope.$applyAsync(function () {
          setPages(getMin(self.pages.length + 10, self.total));
        });
      }
    });
    
    $scope.$watch('$pageSelect.total', function (total) {
      setPages(getMin(Math.max(self.pages.length, 10), total));
    });
  }

  Controller.$inject = ['$element', '$scope'];

  return {
    bindToController: {
      total: '@'
    },
    controller: Controller,
    controllerAs: '$pageSelect'
  };
}

})(window, angular);
(function () {
    'use strict';
    angular.module('su-avaliacao').config(["$stateProvider", function ($stateProvider) {

        $stateProvider
            .state('avaliacao', {
                abstract: true,
                template: '<div style="padding-top: 0px; padding-bottom: 0px;" id="su-avaliacao" ui-view autoscroll="true"></div>',
                url: '/avaliacao',
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/app/language');
                    }]
                }
            })

            .state('avaliacao.CONSULTAR_DADOS_AVALIACAO_IMOVEL', {
                url: '/consultarAvaliacaoImoveis',
                templateUrl: 'scripts-avaliacao/pages/imovel-avaliacao/views/consultarDadosAvaliacao.html',
                controller: 'consultarDadosAvaliacaoController as consultaDadosAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'CONSULTAR AVALIAÇÕES DO IMÓVEL'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/imovel-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.DETALHAR_IMOVEL_AVALIACAO', {
                url: '/detalharImovel/:ripDetalhar',
                templateUrl: 'scripts-avaliacao/pages/avaliacao/views/detalharImovelAvaliacao.html',
                controller: 'detalharImovelAvaliacaoController as detalharImovelAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'INCLUIR DADOS DAS AVALIAÇÕES',
                    parent: 'avaliacao.CONSULTAR_DADOS_AVALIACAO_IMOVEL'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CONSULTAR_DETALHAR_IMOVEL_AVALIACAO', {
                url: '/consultarDetalharImovel?rip',
                templateUrl: 'scripts-avaliacao/pages/avaliacao/views/detalharImovelAvaliacao.html',
                controller: 'detalharImovelAvaliacaoController as detalharImovelAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'INCLUIR DADOS DAS AVALIAÇÕES',
                    parent: 'avaliacao.CONSULTAR_DADOS_AVALIACAO_IMOVEL'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CONSULTAR_DETALHAR_IMOVEL_CONTABILIDADE', {
                url: '/consultarDetalharImovelContabilidade?rip',
                templateUrl: 'scripts-avaliacao/pages/avaliacao/views/detalharImovelContabilidade.html',
                controller: 'detalharImovelContabilidadeController as detalharImovelContabilidadeCtrl',
                ncyBreadcrumb: {
                    label: 'INCLUIR DADOS DAS AVALIAÇÕES',
                    parent: 'avaliacao.CONSULTAR_DADOS_AVALIACAO_IMOVEL'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.DETALHAR_IMOVEL_AVALIACAO_MENSAGEM', {
                url: '/detalharImovel/:rip/:mensagem_sucesso',
                templateUrl: 'scripts-avaliacao/pages/avaliacao/views/detalharImovelAvaliacao.html',
                controller: 'detalharImovelAvaliacaoController as detalharImovelAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'INCLUIR DADOS DAS AVALIAÇÕES',
                    parent: 'avaliacao.CONSULTAR_DADOS_AVALIACAO_IMOVEL'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CADASTRAR_LAUDO_AVALIACAO', {
                url: '/cadastrarLaudoAvaliacao/:rip',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoAvaliacao.html',
                controller: 'cadastrarLaudoAvaliacaoController as cadastrarLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CADASTRAR_LAUDO_AVALIACAO_LOCACAO', {
                url: '/cadastrarLaudoAvaliacaoLocacao/:rip',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoAvaliacaoLocacao.html',
                controller: 'cadastrarLaudoAvaliacaoLocacaoController as cadastrarLaudoAvaliacaoLocacaoCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.EDITAR_LAUDO_AVALIACAO', {
                url: '/editarLaudoAvaliacao/:rip/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoAvaliacao.html',
                controller: 'cadastrarLaudoAvaliacaoController as cadastrarLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'EDITAR LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.EDITAR_LAUDO_AVALIACAO_LOCACAO', {
                url: '/editarLaudoAvaliacaoLocacao/:rip/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoAvaliacaoLocacao.html',
                controller: 'cadastrarLaudoAvaliacaoLocacaoController as cadastrarLaudoAvaliacaoLocacaoCtrl',
                ncyBreadcrumb: {
                    label: 'EDITAR LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CADASTRAR_RELATORIO_VALOR_REFERENCIA', {
                url: '/cadastrarRelatorioValorReferencia/:rip',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoRelatorio.html',
                controller: 'cadastrarRelatorioValorReferenciaController as cadastrarRelatorioValorReferenciaCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR RELATÓRIO VALOR REFERÊNCIA',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.EDITAR_RELATORIO_VALOR_REFERENCIA', {
                url: '/editarRelatorioValorReferencia/:rip/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoRelatorio.html',
                controller: 'cadastrarRelatorioValorReferenciaController as cadastrarRelatorioValorReferenciaCtrl',
                ncyBreadcrumb: {
                    label: 'EDITAR RELATÓRIO VALOR REFERÊNCIA',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.DETALHAR_LAUDO_AVALIACAO', {
                url: '/detalharLaudoAvaliacao/:rip/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoAvaliacao.html',
                controller: 'detalharLaudoAvaliacaoController as detalharLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'DETALHAR LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.DETALHAR_LAUDO_AVALIACAO_LOCACAO', {
                url: '/detalharLaudoAvaliacaoLocacao/:rip/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoAvaliacaoLocacao.html',
                controller: 'detalharLaudoAvaliacaoController as detalharLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'DETALHAR LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.DETALHAR_RELATORIO_VALOR_REFERENCIA', {
                url: '/detalharRelatorioValorReferencia/:rip/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoRelatorio.html',
                controller: 'detalharLaudoAvaliacaoController as detalharLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'DETALHAR LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CANCELAR_LAUDO_AVALIACAO', {
                url: '/cancelarLaudoAvaliacao/:rip/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/cancelarLaudoAvaliacao.html',
                controller: 'cancelarLaudoAvaliacaoController as cancelarLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'CANCELAR LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO', {
                url: '/consultarLaudoRelatorioAvaliacao',
                templateUrl: 'scripts-avaliacao/pages/laudo-relatorio-avaliacao/views/consultarLaudoRelatorioAvaliacao.html',
                controller: 'consultarLaudoRelatorioAvaliacaoController as consultarLaudoRelatorioAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'CONSULTAR LAUDO RELATÓRIO AVALIAÇÃO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-relatorio-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CADASTRAR_LAUDO_RELATORIO_AVALIACAO', {
                url: '/cadastrarLaudoRelatorioAvaliacao?rip',
                templateUrl: 'scripts-avaliacao/pages/laudo-relatorio-avaliacao/views/cadastrarLaudoRelatorioAvaliacao.html',
                controller: 'cadastrarLaudoRelatorioAvaliacaoController as cadastrarLaudoRelatorioAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR LAUDO RELATÓRIO AVALIAÇÃO',
                    parent: 'avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-relatorio-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO', {
                url: '/cadastrarLaudoRelatorioAvaliacao?rip?idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-relatorio-avaliacao/views/cadastrarLaudoRelatorioAvaliacao.html',
                controller: 'cadastrarLaudoRelatorioAvaliacaoController as cadastrarLaudoRelatorioAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'EDITAR LAUDO RELATÓRIO AVALIAÇÃO',
                    parent: 'avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-relatorio-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.DETALHAR_LAUDO_RELATORIO_AVALIACAO', {
                url: '/detalharLaudoRelatorioAvaliacao/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-relatorio-avaliacao/views/cadastrarLaudoRelatorioAvaliacao.html',
                controller: 'cadastrarLaudoRelatorioAvaliacaoController as cadastrarLaudoRelatorioAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'DETALHAR LAUDO RELATÓRIO AVALIAÇÃO',
                    parent: 'avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-relatorio-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CONSULTAR_IMOVEL_LAUDO', {
                url: '/consultarImovelLaudo',
                templateUrl: 'scripts-avaliacao/pages/imovel-laudo-relatorio/views/consultarImovelLaudo.html',
                controller: 'consultarImovelLaudoController as consultarImovelLaudoCtrl',
                ncyBreadcrumb: {
                    label: 'CONSULTAR IMÓVEL',

                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/imovel-laudo-relatorio/language');
                    }]
                }
            })

            .state('avaliacao.INFORMAR_VALOR_CUB', {
                url: '/informarValorCub',
                templateUrl: 'scripts-avaliacao/pages/cub/views/informarValorCub.html',
                controller: 'informarCubAvaliacaoController as informarCubAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'INFORMAR CUB',
                    parent: 'avaliacao.CADASTRAR_LAUDO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/cub/language');
                    }]
                }
            })

            .state('avaliacao.INFORMAR_VALOR_CUB_LAUDO_RELATORIO', {
                url: '/informarValorCubLaudo',
                templateUrl: 'scripts-avaliacao/pages/cub/views/informarValorCubLaudoRelatorio.html',
                controller: 'informarCubLaudoRelatorioController as informarCubLaudoRelatoioCtrl',
                ncyBreadcrumb: {
                    label: 'INFORMAR CUB',
                    parent: 'avaliacao.CADASTRAR_LAUDO_RELATORIO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/cub/language');
                    }]
                }
            })

            .state('avaliacao.ASSINAR_LAUDO_RELATORIO_AVALIACAO', {
                url: '/assinarLaudoRelatorio/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-relatorio-avaliacao/views/cadastrarLaudoRelatorioAvaliacao.html',
                controller: 'cadastrarLaudoRelatorioAvaliacaoController as cadastrarLaudoRelatorioAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'ASSINAR LAUDO RELATÓRIO',
                    parent: 'avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-relatorio-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.VISUALIZAR_LAUDO_RELATORIO_AVALIACAO_PDF', {
                url: '/visualizarLaudoRelatorioPdf/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/laudo-relatorio-avaliacao/views/visualizarLaudoRelatorioPdf.html',
                controller: 'visualizarLaudoRelatorioPdfController as visualizarLaudoRelatorioPdfCtrl',
                ncyBreadcrumb: {
                    label: 'ASSINAR LAUDO RELATÓRIO',
                    parent: 'avaliacao.CONSULTAR_LAUDO_RELATORIO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-relatorio-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.INSERIR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO', {
                url: '/inserirNotaTecnicaHomologacao',
                templateUrl: 'scripts-avaliacao/pages/nota-tecnica/views/inserirNotaTecnicaHomologacaoLaudoAvaliacao.html',
                controller: 'inserirNotaTecnicaHomologacaoLaudoAvaliacaoController as inserirNotaTecnicaHomologacaoLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'NOTA TÉCNICA HOMOLOGAÇÃO LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.CADASTRAR_LAUDO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/nota-tecnica/language');
                    }]
                }
            })

            .state('avaliacao.ASSINAR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO', {
                url: '/assinarNotaTecnicaHomologacao/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/nota-tecnica/views/assinarNotaTecnicaHomologacaoLaudoAvaliacao.html',
                controller: 'assinarNotaTecnicaHomologacaoLaudoAvaliacaoController as assinarNotaTecnicaHomologacaoLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'ASSINAR NOTA TÉCNICA HOMOLOGAÇÃO LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/nota-tecnica/language');
                    }]
                }
            })

            .state('avaliacao.VISUALIZAR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO', {
                url: '/visualizarNotaTecnicaHomologacao',
                templateUrl: 'scripts-avaliacao/pages/nota-tecnica/views/visualizarNotaTecnicaHomologacaoLaudoAvaliacao.html',
                controller: 'visualizarNotaTecnicaHomologacaoLaudoAvaliacaoController as visualizarNotaTecnicaHomologacaoLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'VISUALIZAR NOTA TÉCNICA HOMOLOGAÇÃO LAUDO AVALIAÇÃO',
                    parent: 'avaliacao.INSERIR_NOTA_TECNICA_HOMOLOGACAO_LAUDO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/nota-tecnica/language');
                    }]
                }
            })

            .state('avaliacao.SELECIONAR_LAUDO_RELATORIO_AGUARDANDO_VINCULACAO', {
                url: '/selecionarLaudoRelatorio',
                templateUrl: 'scripts-avaliacao/pages/laudo-avaliacao/views/selecionarLaudoRelatorio.html',
                controller: 'selecionarLaudoRelatorioVinculacaoController as selecionarLaudoRelatorioVinculacaoCtrl',
                ncyBreadcrumb: {
                    label: 'SELECIONAR LAUDO RELATORIO',
                    parent: 'avaliacao.CADASTRAR_LAUDO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.ASSINAR_TERMO_RESPONSABILIDADE_LAUDO_AVALIACAO', {
                url: '/assinarTermoResponsabilidadeLaudoAvaliacao',
                templateUrl: 'scripts-avaliacao/pages/termo-responsabilidade/views/assinarTermoResponsabilidade.html',
                controller: 'assinarTermoResponsabilidadeLaudoAvaliacaoController as assinarTermoResponsabilidadeLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'ASSINAR TERMO RESPONSABILIDADE',
                    parent: 'avaliacao.CADASTRAR_LAUDO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/termo-responsabilidade/language');
                    }]
                }
            })

            .state('avaliacao.ASSINAR_TERMO_RESPONSABILIDADE_REVALIDACAO_LAUDO_AVALIACAO', {
                url: '/assinarTermoRevalidacaoLaudoAvaliacao',
                templateUrl: 'scripts-avaliacao/pages/termo-responsabilidade/views/assinarTermoResponsabilidade.html',
                controller: 'assinarTermoResponsabilidadeRevalidacaoLaudoAvaliacaoController as assinarTermoResponsabilidadeRevalidacaoLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'ASSINAR TERMO RESPONSABILIDADE',
                    parent: 'avaliacao.REVALIDAR_LAUDO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/termo-responsabilidade/language');
                    }]
                }
            })

            .state('avaliacao.REVALIDAR_LAUDO_AVALIACAO', {
                url: '/revalidarLaudoAvaliacao/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/revalidacao-laudo-avaliacao/views/revalidarLaudoRelatorioAvaliacao.html',
                controller: 'revalidarLaudoAvaliacaoController as revalidarLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'REVALIDAR LAUDO AVALIACAO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/revalidacao-laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.VISUALIZAR_DOCUMENTO_REVALIDAR_LAUDO_AVALIACAO', {
                url: '/visualizarDocumenetoRevalidarLaudoAvaliacao',
                templateUrl: 'scripts-avaliacao/pages/revalidacao-laudo-avaliacao/views/visualizarRevalidarLaudoAvaliacao.html',
                controller: 'visualizarRevalidarLaudoAvaliacaoController as visualizarRevalidarLaudoAvaliacaoControllerCtrl',
                ncyBreadcrumb: {
                    label: 'VISUALIZAR DOCUMENTO REVALIDAR LAUDO AVALIACAO',
                    parent: 'avaliacao.REVALIDAR_LAUDO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/revalidacao-laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.ASSINAR_REVALIDAR_LAUDO_AVALIACAO', {
                url: '/assinarRevalidarLaudoAvaliacao/:idLaudo',
                templateUrl: 'scripts-avaliacao/pages/revalidacao-laudo-avaliacao/views/assinarRevalidarLaudoAvaliacao.html',
                controller: 'assinarRevalidarLaudoAvaliacaoController as assinarRevalidarLaudoAvaliacaoCtrl',
                ncyBreadcrumb: {
                    label: 'REVALIDAR LAUDO RELATORIO AVALIACAO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/revalidacao-laudo-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CONSULTAR_ACOES_AVALIACAO', {
                url: '/consultarAcoesAvaliacao',
                templateUrl: 'scripts-avaliacao/pages/validacao-acoes-avaliacao/views/consultarAcoesAvaliacao.html',
                controller: 'consultarAcoesAvaliacaoController as consultarAcoesAvaliacaoControllerCtrl',
                ncyBreadcrumb: {
                    label: 'CONSULTAR AÇÕES DE AVALIAÇÃO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/validacao-acoes-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CONSULTAR_ACOES_AVALIACAO_MENSAGEM', {
                url: '/consultarAcoesAvaliacao/:mensagem_sucesso',
                templateUrl: 'scripts-avaliacao/pages/validacao-acoes-avaliacao/views/consultarAcoesAvaliacao.html',
                controller: 'consultarAcoesAvaliacaoController as consultarAcoesAvaliacaoControllerCtrl',
                ncyBreadcrumb: {
                    label: 'CONSULTAR AÇÕES DE AVALIAÇÃO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/validacao-acoes-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.VALIDAR_ACOES_AVALIACAO', {
                url: '/validarAcoesAvaliacao/:idAcaoAvaliacao',
                templateUrl: 'scripts-avaliacao/pages/validacao-acoes-avaliacao/views/validarAcoesAvaliacao.html',
                controller: 'validarAcoesAvaliacaoController as validarAcoesAvaliacaoControllerCtrl',
                ncyBreadcrumb: {
                    label: 'AÇÕES DE AVALIAÇÃO',
                    parent: 'avaliacao.CONSULTAR_ACOES_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/validacao-acoes-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.VISUALIZAR_HISTORICO_ACOES_AVALIACAO', {
                url: '/visualizarHistoricoAcoesAvaliacao/:idAcaoAvaliacao',
                templateUrl: 'scripts-avaliacao/pages/validacao-acoes-avaliacao/views/visualizarHistoricoAcoesAvaliacao.html',
                controller: 'HistoricoAcoesAvaliacaoController as HistoricoAcoesAvaliacaoControllerCtrl',
                ncyBreadcrumb: {
                    label: 'AÇÕES DE AVALIAÇÃO',
                    parent: 'avaliacao.CONSULTAR_ACOES_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/validacao-acoes-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.ACESSO_NEGADO', {
                url: '/acessoNegado',
                templateUrl: 'scripts-avaliacao/app/views/acesso.negado.tmpl.html',
                ncyBreadcrumb: {
                    label: 'ACESSO NEGADO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/app/language');
                    }]
                }
            })

            .state('avaliacao.SERVICO_INDISPONIVEL', {
                url: '/servicoIndisponivel',
                templateUrl: 'scripts-avaliacao/app/views/servico.indisponivel.tmpl.html',
                ncyBreadcrumb: {
                    label: 'SERVIÇO INDISPONÍVEL'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/app/language');
                    }]
                }
            })

            .state('avaliacao.DADOS_PESSOAS', {
                url: '/dadosPessoas',
                templateUrl: 'scripts-avaliacao/pages/dados-pessoas/views/dadosPessoas.html',
                controller: 'dadosPessoasController',
                controllerAs: 'dadosPessoasCtrl',
                ncyBreadcrumb: {
                    label: 'Dados Pessoas'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/dados-pessoas/language')
                    }]
                }
            })
            .state('avaliacao.DADOS_PESSOAS_JURIDICA', {
                url: '/dadosPessoasJuridica',
                templateUrl: 'scripts-avaliacao/pages/dados-pessoas/views/dadosPessoasJuridica.html',
                controller: 'dadosPessoasJuridicaController',
                controllerAs: 'dadosPessoasJuridicaCtrl',
                ncyBreadcrumb: {
                    label: 'Dados Pessoas'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/dados-pessoas/language')
                    }]
                }
            })

            .state('avaliacao.CADASTRAR_AVALIACAO_PVG_MUNICIPIO', {
                url: '/cadastrarAvaliacaoPvgMunicipio/:rip',
                templateUrl: 'scripts-avaliacao/pages/pvg-municipio-avaliacao/views/cadastrarAvaliacaoPvgMunicipio.html',
                controller: 'cadastrarAvaliacaoPvgMunicipioController as cadastrarAvaliacaoPvgMunicipioControllerCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR AVALIAÇÃO PVG MUNICÍPIO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/pvg-municipio-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.DETALHAR_AVALIACAO_PVG_MUNICIPIO', {
                url: '/detalharAvaliacaoPvgMunicipio/:rip/:idPvg',
                templateUrl: 'scripts-avaliacao/pages/pvg-municipio-avaliacao/views/cadastrarAvaliacaoPvgMunicipio.html',
                controller: 'cadastrarAvaliacaoPvgMunicipioController as cadastrarAvaliacaoPvgMunicipioControllerCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR AVALIAÇÃO PVG MUNICÍPIO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/pvg-municipio-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CANCELAR_AVALIACAO_PVG_MUNICIPIO', {
                url: '/cancelarAvaliacaoPvgMunicipio/:rip/:idPvg',
                templateUrl: 'scripts-avaliacao/pages/pvg-municipio-avaliacao/views/cancelarAvaliacaoPvgMunicipio.html',
                controller: 'cancelarAvaliacaoPvgMunicipioController as cancelarAvaliacaoPvgMunicipioControllerCtrl',
                ncyBreadcrumb: {
                    label: 'CANCELAR AVALIAÇÃO PVG MUNICÍPIO',
                    parent: 'avaliacao.CANCELAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/pvg-municipio-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CADASTRAR_AVALIACAO_PVG_INCRA', {
                url: '/cadastrarAvaliacaoPvgIncra/:rip',
                templateUrl: 'scripts-avaliacao/pages/pvg-incra-avaliacao/views/cadastrarAvaliacaoPvgIncra.html',
                controller: 'cadastrarAvaliacaoPvgIncraController as cadastrarAvaliacaoPvgIncraControllerCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR AVALIAÇÃO PVG INCRA',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/pvg-incra-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.DETALHAR_AVALIACAO_PVG_INCRA', {
                url: '/detalharAvaliacaoPvgIncra/:rip/:idPvg',
                templateUrl: 'scripts-avaliacao/pages/pvg-incra-avaliacao/views/cadastrarAvaliacaoPvgIncra.html',
                controller: 'cadastrarAvaliacaoPvgIncraController as ccadastrarAvaliacaoPvgIncraControllerCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR AVALIAÇÃO PVG INCRA',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/pvg-incra-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CANCELAR_AVALIACAO_PVG_INCRA', {
                url: '/cancelarAvaliacaoPvgIncra/:rip/:idPvg',
                templateUrl: 'scripts-avaliacao/pages/pvg-incra-avaliacao/views/cancelarAvaliacaoPvgIncra.html',
                controller: 'cancelarAvaliacaoPvgIncraController as cancelarAvaliacaoPvgIncraControllerCtrl',
                ncyBreadcrumb: {
                    label: 'CANCELAR AVALIAÇÃO PVG INCRA',
                    parent: 'avaliacao.CANCELAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/pvg-incra-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.DESVINCULAR_AVALIACAO_PVG_SPU', {
                url: '/desvincularAvaliacaoPvgSpu/:rip',
                templateUrl: 'scripts-avaliacao/pages/pvg-spu-avaliacao/views/desvincularAvaliacaoPvgSpu.html',
                controller: 'desvincularAvaliacaoPvgSpuController as desvincularAvaliacaoPvgSpuControllerCtrl',
                ncyBreadcrumb: {
                    label: 'DESVINCULAR AVALIAÇÃO PVG SPU',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/pvg-spu-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.CADASTRAR_AVALIACAO_PVG_SPU', {
                url: '/cadastrarAvaliacaoPvgSpu/:rip',
                templateUrl: 'scripts-avaliacao/pages/pvg-spu-avaliacao/views/cadastrarAvaliacaoPvgSpu.html',
                controller: 'cadastrarAvaliacaoPvgSpuController as cadastrarAvaliacaoPvgSpuControllerCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR AVALIAÇÃO PVG SPU',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/pvg-spu-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.EDITAR_FATORES_AVALIACAO_PVG_SPU', {
                url: '/cadastrarAvaliacaoPvgSpu/:rip/:idPvg/editar',
                templateUrl: 'scripts-avaliacao/pages/pvg-spu-avaliacao/views/cadastrarAvaliacaoPvgSpu.html',
                controller: 'cadastrarAvaliacaoPvgSpuController as cadastrarAvaliacaoPvgSpuControllerCtrl',
                ncyBreadcrumb: {
                    label: 'EDITAR FATORES AVALIAÇÃO PVG SPU',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/pvg-spu-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.DETALHAR_AVALIACAO_PVG_SPU', {
                url: '/detalharAvaliacaoPvgSpu/:rip/:idPvg',
                templateUrl: 'scripts-avaliacao/pages/pvg-spu-avaliacao/views/cadastrarAvaliacaoPvgSpu.html',
                controller: 'cadastrarAvaliacaoPvgSpuController as cadastrarAvaliacaoPvgSpuControllerCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR AVALIAÇÃO PVG SPU',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/pvg-spu-avaliacao/language');
                    }]
                }
            })

            .state('avaliacao.APRESENTAR_DADOS_UTILIZACAO', {
                url: '/apresentarDadosUtilizacao/:rip',
                templateUrl: 'scripts-avaliacao/pages/dados-utilizacao/views/apresentarDadosUtilizacao.html',
                controller: 'ApresentarDadosUtilizacaoController as ApresentarDadosUtilizacaoCtrl',
                ncyBreadcrumb: {
                    label: 'APRESENTAR DADOS UTILIZAÇÃO',
                    parent: 'avaliacao.DETALHAR_IMOVEL_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/dados-utilizacao/language');
                    }]
                }
            })

            .state('avaliacao.CADASTRAR_DOCUMENTO_EDIFICACAO', {
                url: '/cadastrarDocumentoEdificacao',
                templateUrl: 'scripts-avaliacao/pages/documento-edificacao/views/cadastrarDocumentoEdificacao.html',
                controller: 'cadastrarDocumentoEdificacaoController as cadastrarDocumentoEdificacaoCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR DOCUMENTO EDIFICAÇÃO',
                    parent: 'avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/app/language');
                    }]
                }
            })

            .state('avaliacao.INSERIR_AMOSTRA', {
                url: '/inserirAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/inserirAmostra.html',
                controller: 'inserirAmostraController as inserirAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'INSERIR DADO DE MERCADO',
                    parent: 'avaliacao.CONSULTAR_AMOSTRA'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.CONSULTAR_AMOSTRA', {
                url: '/consultarAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/consultarAmostra.html',
                controller: 'consultarAmostraController as consultarAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'CONSULTAR DADOS DE MERCADO',
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.EDITAR_AMOSTRA', {
                url: '/editarAmostra/:idAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/inserirAmostra.html',
                controller: 'inserirAmostraController as inserirAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'EDITAR DADO DE MERCADO',
                    parent: 'avaliacao.CONSULTAR_AMOSTRA'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.DETALHAR_AMOSTRA', {
                url: '/detalharAmostra/:idAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/inserirAmostra.html',
                controller: 'inserirAmostraController as inserirAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'DETALHAR DADO DE MERCADO',
                    parent: 'avaliacao.CONSULTAR_AMOSTRA'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.CANCELAR_AMOSTRA', {
                url: '/cancelarAmostra/:idAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/cancelarAmostra.html',
                controller: 'cancelarAmostraController as cancelarAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'CANCELAR DADO DE MERCADO',
                    parent: 'avaliacao.CONSULTAR_AMOSTRA'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.SELECIONAR_AMOSTRA', {
                url: '/selecionarAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/selecionarAmostra.html',
                controller: 'selecionarAmostraController as selecionarAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'SELECIONAR DADO DE MERCADO',
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.ADICIONAR_VARIAVEL_AMOSTRA', {
                url: '/adicionarVariavelAmostra/:idAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/adicionarVariavelAmostra.html',
                controller: 'adicionarVariavelAmostraController as adicionarVariavelAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'ADICIONAR VARIÁVEL DADO DE MERCADO',
                    parent: 'avaliacao.CONSULTAR_AMOSTRA'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.INSERIR_AMOSTRA_VARIAVEL_AMOSTRA', {
                url: '/inserirAmostraVariavelAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/adicionarVariavelAmostra.html',
                controller: 'adicionarVariavelAmostraController as adicionarVariavelAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'INSERIR DADO DE MERCADO',
                    parent: 'avaliacao.CONSULTAR_AMOSTRA'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.GERAR_PLANILHA_AMOSTRA', {
                url: '/gerarPlanilhaAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/gerarPlanilhaAmostra.html',
                controller: 'gerarPlanilhaAmostraController as gerarPlanilhaAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'GERAR PLANILHA DADO DE MERCADO',
                    parent: 'avaliacao.CONSULTAR_AMOSTRA'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.UPLOAD_PLANILHA_AMOSTRA', {
                url: '/uploadPlanilhaAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/uploadPlanilhaAmostra.html',
                controller: 'uploadPlanilhaAmostraController as uploadPlanilhaAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'UPLOAD PLANILHA DADO DE MERCADO',
                    parent: 'avaliacao.CONSULTAR_AMOSTRA'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.CONSULTAR_UPLOAD_AMOSTRA', {
                url: '/consultarUploadAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/consultarUploadAmostra.html',
                controller: 'consultarUploadAmostraController as consultarUploadAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'CONSULTAR UPLOAD DADO DE MERCADO',
                    parent: 'avaliacao.CONSULTAR_AMOSTRA'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.COMPLETAR_DADOS_AMOSTRA', {
                url: '/completarDadosAmostra/:idAmostra',
                templateUrl: 'scripts-avaliacao/pages/amostra/views/completarDadosAmostra.html',
                controller: 'completarDadosAmostraController as completarDadosAmostraCtrl',
                ncyBreadcrumb: {
                    label: 'COMPLETAR DADO DE MERCADO',
                    parent: 'avaliacao.CONSULTAR_AMOSTRA'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/pages/amostra/language');
                    }]
                }
            })

            .state('avaliacao.CADASTRAR_VALOR_EDIFICACAO', {
                url: '/cadastrarValorEdificacao',
                templateUrl: 'scripts-avaliacao/pages/valor-edificacao/views/cadastrarValorEdificacao.html',
                controller: 'cadastrarValorEdificacaoController as cadastrarValorEdificacaoCtrl',
                ncyBreadcrumb: {
                    label: 'CADASTRAR VALOR EDIFICAÇÃO',
                    parent: 'avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/app/language');
                    }]
                }
            })

            .state('avaliacao.CALCULAR_AREA_EQUIVALENTE', {
                url: '/calcularAreaEquivalente',
                templateUrl: 'scripts-avaliacao/pages/area-equivalente/views/calcularAreaEquivalente.html',
                controller: 'calcularAreaEquivalenteController as calcularAreaEquivalenteCtrl',
                ncyBreadcrumb: {
                    label: 'CALCULAR ÁREA EQUIVALENTE',
                    parent: 'avaliacao.EDITAR_LAUDO_RELATORIO_AVALIACAO'
                },
                resolve: {
                    trans: ["Traducoes", function (Traducoes) {
                        return new Traducoes('scripts-avaliacao/app/language');
                    }]
                }
            });

    }]);
})();
(function () {
"use strict";
angular.module("su-avaliacao")

    .constant("ENV", "dev")

    .constant("VERSION", "1.0.0")

    .constant("ol", ol)
    //conflita na hora de publicar o frontend unificado
    //.constant('urlBase',  '/avaliacao/api/')

    .constant("USUARIO_LOGADO", "usuarioLogado");
})();
(function () {
    'use strict';

    configLocalStorage.$inject = ["localStorageServiceProvider"];
    configProgressBar.$inject = ["cfpLoadingBarProvider"];
    appConfig.$inject = ["$provide"];
    configDataFormatacao.$inject = ["$mdDateLocaleProvider", "moment"];
    angular.module('su-avaliacao')
        .config(configLocalStorage)
        .config(configProgressBar)
        .config(appConfig)
        .config(["$httpProvider", function ($httpProvider) {
            $httpProvider.interceptors.push('errorHandlerInterceptorAvaliacao');
        }])
        .config(configDataFormatacao);

    function configLocalStorage(localStorageServiceProvider) {
        localStorageServiceProvider.setPrefix('avaliacao');
    }

    function configProgressBar(cfpLoadingBarProvider) {
        cfpLoadingBarProvider.includeBar = true;
    }

    function configDataFormatacao($mdDateLocaleProvider, moment) {
        $mdDateLocaleProvider.formatDate = function (date) {
            return date ? moment(date).format('DD/MM/YYYY') : '';
        };

        $mdDateLocaleProvider.parseDate = function (dateString) {
            if (dateString == null || dateString.length == 0) {
                return null;
            }

            var m = moment(dateString, 'DD/MM/YYYY', true);
            return m.isValid() ? m.toDate() : '';
        };
    }

    function appConfig($provide){

        $provide.decorator('mdDatepickerDirective', ["$delegate", function ($delegate) {
            var directive = $delegate[0];

            var template = directive.template;
            var link = directive.link;
            directive.compile = function () {
                return function (scope, element, attrs, ctrl) {

                    link.apply(this, arguments);
                    var inputNgModelControl = scope.ctrl.ngInputElement.controller('ngModel');

                    // for the first initialization
                    inputNgModelControl.$formatters.push(function (value) {
                        return scope.ctrl.$mdDateLocale.formatDate(scope.ctrl.ngInputElement.controller('mdDatepicker').ngModelCtrl.$viewValue);
                    });

                    // for transfer validation
                    scope.$watch(function () {
                        return scope.ctrl.ngInputElement.controller('ngModel').$error.mask;
                    }, function (newValue) {
                        ctrl[0].$setValidity('mask', !newValue)
                    })
                };
            };
            directive.template = function (tElement, tAttrs) {
                var originalTemplate = template.apply(this, arguments);
                // adding mask and ng-model to mdDatepicker's input
                var element = angular.element(originalTemplate);
                element.find('input').attr('mask', '3?9/1?9/9999');
                element.find('input').attr('ng-model', "ctrl.dateInput");

                // new tempalte with mask and ng-model attributes
                var newTemplate =  '';
                for (var i = 0; i < element.length; i++) {
                    newTemplate += element[i].outerHTML
                }
                return newTemplate ;

            };
            return $delegate;
        }]);

    }

})();

angular.module("su-avaliacao").run(["$templateCache", function($templateCache) {$templateCache.put("scripts-avaliacao/app/language/pt-BR.json","{ \"label-ministerio-planejamento\": \"MINISTÉRIO DO PLANEJAMENTO, DESENVOLVIMENTO E GESTÃO\", \"label-secretaria-patrimonio-da-uniao\": \"Secretaria do Patrimônio da União\", \"label-nota-tecnica-homologacao-laudo-avaliacao\": \"Nota Técnica de Homologação do Laudo de Avaliação\", \"button-adicionar-arquivo\": \"Adicionar\", \"button-fechar\": \"Fechar\", \"button-salvar\": \"Salvar\", \"button-fechar-janela\":\"Fechar janela\", \"button-gravar\": \"Gravar\", \"button-confirmar\": \"Confirmar\", \"button-incluir-avaliacao\": \"Incluir Avaliação\", \"button-limpar\": \"Limpar\", \"button-voltar\": \"Voltar\", \"button-novo\" : \"Novo\", \"button-editar\" : \"Editar\", \"button-alterar\" : \"Alterar\", \"button-excluir\" : \"Excluir\", \"button-retornar\": \"Retornar\", \"button-detalhar\": \"Detalhar Laudo\", \"button-adicionar\": \"Adicionar\", \"button-consultar\" : \"Consultar\", \"button-localizacao\": \"Ver localização\", \"button-pesquisar\": \"Pesquisar\", \"button-selecionar-arquivo\": \"Selecionar Arquivo\", \"button-visualizar-no-mapa\": \"Visualizar no Mapa\", \"button-salvar-rascunho\": \"Salvar Rascunho\", \"button-avancar\": \"Avançar\", \"button-inserir-nova-matricula-transcricao\": \"Inserir Nova Matrícula/Transcrição\", \"button-inserir-novo-documento-cartorial\": \"Inserir Novo Documento Cartorial\", \"button-assinar\": \"Assinar\", \"button-incluir-laudo-relatorio\": \"Incluir novo Laudo/Relatório\", \"button-inserir-novo-documento\": \"Incluir novo documento\", \"button-incluir-dado\": \"Incluir Dado\", \"button-importar-dado\": \"Importar Dado\", \"button-utilizar-dado-cadastrado\": \"Utilizar Dado Cadastrado\", \"button-incluir-relatorio-valor-referencia\": \"Incluir Relatório de Valor de Referência\", \"button-incluir-laudo-avaliacao\": \"Incluir Laudo de Avaliação\", \"button-novo-laudo\": \"Incluir Novo Laudo\", \"button-novo-relatorio\": \"Incluir Novo Relatório\", \"button-exportar-planilha\": \"Exportar planilha de amostras\", \"btn-salvar-rascunho-cadastrar-avaliacao\": \"Salvar rascunho e cadastrar avaliação\", \"btn-nao-cadastrar-avaliacao\": \"Não cadastrar avaliação\", \"btn-sim\": \"Sim\", \"btn-nao\": \"Não\", \"button-informar-cub\": \"Informar CUB\", \"idade-aparente-ano\": \"Idade Aparente (Ano)\", \"label-ajuda\": \"Ajuda\", \"label-cancelar-avaliacao\": \"Cancelar Avaliação\", \"label-desvincular-trecho\": \"Desvincular Trecho\", \"label-cancelar-laudo\": \"Cancelar\", \"label-cancelar-relatorio\": \"Cancelar Relatório\", \"label-cancelar\": \"Cancelar\", \"label-cep\": \"CEP\", \"label-cidade-exterior\": \"Cidade Exterior\", \"label-configuracoes-aplicacao\": \"Configurações da aplicação\", \"label-consulta-de-imoveis\": \"Consulta de Imóveis\", \"label-detalhar-laudo\": \"Detalhar\", \"label-editar-laudo\": \"Editar\", \"label-remover-laudo\": \"Remover rascunho\", \"label-remover-relatorio\": \"Remover rascunho\", \"label-assinar-laudo\": \"Assinar laudo\", \"label-detalhar-relatorio\": \"Detalhar Relatório\", \"label-editar-relatorio\": \"Editar Relatório\", \"label-assinar-relatorio\": \"Assinar Relatório\", \"label-historico-acoes\": \"Histórico de Ações\", \"label-assinar-revalidacao\": \"Assinar revalidação\", \"label-assinar-nota-tecnica-homologacao\": \"Assinar homologação\", \"label-documento\": \"Documento\", \"label-nome-documento\": \"Nome do Documento\", \"label-excluir-documento\": \"Excluir documento\", \"label-excluir-arquivo\": \"Excluir arquivo\", \"label-incluir-documentos\":\"Incluir documentos\", \"label-editar-responsavel-tecnico\": \"Editar Responsável\", \"label-excluir-responsavel-tecnico\": \"Excluir Responsável\", \"label-incluir-editar-dados-avaliacao\": \"Incluir/Editar Dados de Avaliação\", \"label-inicio-avaliacao-trecho\": \"Data de inicio da primeira avaliação do trecho\", \"label-logradouro\": \"Logradouro\", \"label-municipio\": \"Município\", \"label-nome\": \"Nome\", \"label-numero\": \"Nº\", \"label-nome-arquivo\": \"Nome do Arquivo\", \"label-notificacoes\": \"Notificações\", \"label-numero-do-rip\": \"Nº do RIP\", \"label-pagina-inicial\": \"Página Inicial\", \"label-pais\": \"País\", \"label-revalidar-laudo\": \"Revalidar Laudo\", \"label-revalidar-relatorio\": \"Revalidar Relatório\", \"label-editar-revalidar-laudo\": \"Editar\", \"label-situacao-avaliacao\": \"Situação da Avaliação\", \"label-selecione\": \"Selecione\", \"label-trecho\": \"Trecho\", \"label-uf\": \"UF\", \"label-ultima-atualizacao\": \"Última Atualização\", \"label-valor-do\": \"Valor do\", \"label-valor-terreno\": \"Valor Terreno\", \"label-valor-terreno-r\": \"Valor do Terreno (R$)\", \"label-visualizar-avaliacao\": \"Visualizar Avaliação\", \"label-editar-fatores\": \"Editar Fatores\", \"label-visualizar-pvg\": \"Visualizar Pvg\", \"label-valor-estimado-terreno\": \"Valor Estimado do Terreno\", \"label-valor-estimado-terreno-em\": \"Valor Estimado do Terreno em \", \"label-nome-responsavel\": \"Responsável\", \"label-motivo\": \"Motivo\", \"label-periodo-cadastramento-imovel\":\"Período de Cadastramento do Laudo/Relatório\" , \"label-periodo-assinatura-imovel\": \"Período de Assinatura do Laudo/Relatório\", \"label-tipo\":\"Tipo\", \"label-metodo_avaliacao\":\"Método de Avaliação\", \"label-situacao\":\"Situação\", \"label-numero_laudo_relatorio\":\"Nº do Laudo/Relatório\", \"label-validade\":\"Validade\", \"label-tipo-imovel\":\"Tipo do Imovel\", \"label-natureza-imovel\":\"Natureza do Imóvel\", \"label-bairro-logradouro\":\"Bairro/Logradouro\", \"label-proprietario_titular_imovel\":\"Titular do Imóvel\", \"label-proprietario-titular-imovel\":\"Proprietário/Titular do Imóvel\", \"label-responsavel-tecnico\": \"Responsável Técnico\", \"label-natureza\": \"Natureza\", \"label-propr-titular\": \"Propr./Titular\", \"label-instrumento-destinacao\":\"Instrumento de Destinação\", \"label-codigo-contrato\": \"Código do Contrato\", \"label-tipo-uso\":\"Tipo de Uso\", \"label-especificacao\":\"Especificação\", \"label-descricao\":\"Descrição\", \"label-cpf-cnpj\":\"CPF/CNPJ\", \"label-ug-executora\":\"UG Executora\", \"label-nome-razao\": \"Nome/Razão Social\", \"label-nome-fantasia\": \"Nome Fantasia\", \"label-nome-entidade\": \"Nome da Entidade\", \"label-familia-beneficiada\":\"Família Beneficiada\", \"label-responsavel\":\"Responsável\", \"label-conjuge\":\"Cônjuge\", \"label-area-utilizada\":\"Área Utilizada(m²)\", \"label-endereco\":\"Endereço\", \"label-reclassificar\": \"Reclassificar Uso\", \"label-uso-efetivo\": \"Uso Efetivo\", \"label-uso-potencial\": \"Uso Potencial\", \"label-especificar-tipo-utilizacao\": \"Especificar tipo da utilização\", \"label-m2\": \"m²\", \"label-ha\": \"ha\", \"label-observacao-infraestrutura-urbana\":\"Observações da Infraestrutura Urbana\", \"label-servicos-comunitarios\":\"Serviços Comunitários\", \"label-tipo-proprietario\":\"Tipo Proprietário\", \"label-orgao-entidade\":\"Órgão/Entidade\", \"label-ug-especifica\":\"UG Especifica\", \"label-proprietario-parcial\":\"Proprietário Parcial\", \"label-memorial-descritivo\":\"Memorial Descritivo\", \"label-unidade-medida-area\":\"Unidade de Medida da Área\", \"label-area-total\":\"Área Total\", \"label-area-uniao\":\"Área União\", \"label-fracao-ideal-imovel\":\"Fração Ideal do Imóvel\", \"label-fracao-ideal-uniao\":\"Fração Ideal da União (m²)\", \"label-memorial-descritivo-area-uniao\":\"Memorial Descritivo da Área da União\", \"label-documento-assinado-em\":\"Documento assinado eletônicamente em\", \"label-documento-assinado\": \"Documento assinado eletronicamente\", \"label-assinado-eletronicamente\": \"Documento assinado eletronicamente por\", \"label-dimencao\":\"Dimensões\", \"label-nivel-rua\":\"Nível da Rua\", \"label-cpf\": \"CPF\", \"label-crea-cau\": \"CREA/CAU\", \"label-crea\": \"CREA\", \"label-cau\": \"CAU\", \"label-formato\":\"Formato\", \"label-em\": \"em\", \"label-as\": \"às\", \"label-topografia\":\"Topografia\", \"label-observacao\":\"Observação\", \"label-indice-aproveitamento\":\"Índice de Aproveitamento\", \"label-taxa-ocupacao-maxima\":\"Taxa de Ocupação Máxima\", \"label-gabarito\":\"Gabarito (metros)\", \"label-afastamento-frontal\":\"Afastamento Frontal (metros)\", \"label-afastamento-lateral\":\"Afastamento Lateral (metros)\", \"label-tipo-acessao-benfeitoria\":\"Tipo de Acessão/Benfeitoria\", \"label-tipo-especializacao-edificacao\":\"Tipo de Especialização de Edificação\", \"label-tipo-especializacao-complementar\":\"Tipo de Especialização de Edificação\", \"label-area-contruida\":\"Área Construída\", \"label-tipo-area-projecao\":\"Área de Projeção\", \"label-numero-pavimento\":\"Nº de Pavimentos\", \"label-vaga-garagem-coberta\":\"Nº de Garagem cobertas\", \"label-vaga-garagem-descoberta\":\"Nº de Garagem descobertas\", \"label-idade-aparente\":\"Idade Aparente (anos)\", \"label-idade-aparente-ano\":\"Idade Aparente (Ano)\", \"label-vida-util\":\"Vida Útil (anos)\", \"label-vida-util-ano\": \"Vida Útil (Ano)\", \"label-cub\":\"CUB\", \"label-acoes\": \"Ações\", \"label-divisao-interna\":\"Divisão Interna\", \"label-material-construtivo-interno\":\"Materiais contrutivos internos (piso, paredes, forro, revestimento)\", \"label-estrutura\":\"Estrutura\", \"label-cobertura\":\"Cobertura\", \"label-fachada\":\"Fachada\", \"label-instalação-equipamento\":\"Instalação e equipamentos\", \"label-nome-cartorio\": \"Nome do Cartório\", \"label-identificacao-cartorio\": \"Identificação do Cartório\", \"label-infraestrutura-urbana\": \"Infraestrutura Urbana\", \"label-pavimentacao\": \"Pavimentação\", \"label-rede-agua\": \"Rede de água\", \"label-rede-esgoto\": \"Rede de esgoto\", \"label-iluminacao-publica\": \"Iluminação pública\", \"label-rede-eletrica\": \"Rede elétrica\", \"label-coleta-lixo\": \"Coleta de lixo\", \"label-sarjeta-guia\": \"Sarjeta/guia\", \"label-passeio\": \"Passeio\", \"label-integralmente\": \"Integralmente\", \"label-parcialmente\": \"Parcialmente\", \"label-matricula-transcrição\": \"Matrícula/Transcrição\", \"label-possui-elevador\": \"Possui Elevador\", \"label-numero-dormitorio\": \"Nº de Dormitórios\", \"label-observacao-gerais-unidade-autonoma\": \"Observação Gerais da Unidade Autônoma\", \"label-tipo-projeto\": \"Tipo Projeto\", \"label-observacao-gerais-edificacao\": \"Observações Gerais da Edificação\", \"label-acessabilidade-unidade-autonoma-vagas\": \"Há vagas próximas dos acessos de circulação de pedestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldades de locomoção permanente.\", \"label-acessabilidade-unidade-autonoma-mobilidade\": \"Os acessos ao interior da edificação estão livres de barreiras arquitetonicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.\", \"label-acessabilidade-unidade-autonoma-banheiro\": \"Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoas portadoras de deficiência ou com mobilidade reduzida.\", \"label-acessabilidade-unidade-autonoma-elevador\": \"Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.\", \"label-valores-terreno-benfeitoria\": \"Valores de Terreno e Benfeitoria\", \"label-informar-valores-do-laudo\": \"Informar valores do Laudo\", \"label-estimar-valores\": \"Estimar valores\", \"label-valor-do-terreno-moeda\": \"Valor do Terreno\", \"label-id\": \"ID\", \"label-area-construida\": \"Área Construída\", \"label-valor\": \"Valor\", \"label-valor-total\": \"VALOR TOTAL\", \"label-valor-estimado-total\": \"VALOR ESTIMADO TOTAL\", \"label-valor-estimado-do-terreno\": \"Valor Estimado do Terreno\", \"label-detalhes-do-cub\": \"Detalhes do CUB\", \"label-porcentagem\": \"Porcentagem\", \"label-valor-estimado-cub\": \"Valor Estimado CUB\", \"label-termo-responsabilidade\": \"Termo de Responsabilidade\", \"label-album-fotos\": \"Álbum de Fotos\", \"label-ver-localizacao\": \"Ver Localização\", \"label-observacao-natureza-imovel\": \"Observação da Natureza do Imóvel\", \"label-observacao-geral\": \"Observações Gerais\", \"label-area\": \"Área (m²)\", \"label-tipo-garagem\": \"Tipo Garagem\", \"label-sim\": \"Sim\", \"label-nao\": \"Não\", \"label-laudo-nao-possui-enquadramento-grau-fundamentacao\": \"O laudo de avaliação não possui enquadramento quanto ao Grau fundamentação\", \"label-laudo-nao-possui-enquadramento-resultado-precisao\": \"O laudo de avaliação não possui classificação do resultado quanto à precisão\", \"label-sem-informacoes\": \"Sem informações de Acessibilidade\", \"label-imovel-terreno\": \"Imóvel/Terreno\", \"label-unidade-autonoma\": \"Unidade Autônoma\", \"label-nao-foi-possivel-realizacao-vistoria\": \"Não foi possível a realização da vistoria\", \"label-selecionar-trecho\": \"Selecionar Trecho\", \"label-selecionar\": \"Selecionar\", \"label-alterar-trecho\": \"Alterar Trecho\", \"label-fundamentacao-01-descricao-caracterizacao-imovel\": \"Caracterização do imóvel avaliando\", \"label-fundamentacao-01-grau-complementa-variaveis-analisadas\": \"Completa quanto a todas as variáveis analisadas\", \"label-fundamentacao-01-grau-complementa-variaveis-utilizadas\": \"Completa quanto a todas às variáveis utilizadas no modelo\", \"label-fundamentacao-01-grau-adocao-situacao-paradgma\": \"Adoção situação paradigma\", \"label-fundamentacao-02-descricao-quantidade-minima-dados\": \"Quantidade mínima de dados de mercado, efetivamente utilizados\", \"label-fundamentacao-02-grau-6-numero-variaveis-independentes\": \"6(k+1), onde k é o número de variáveis independentes\", \"label-fundamentacao-02-grau-4-numero-variaveis-independentes\": \"4(k+1), onde k é o número de variáveis independentes\", \"label-fundamentacao-02-grau-3-numero-variaveis-independentes\": \"3(k+1), onde k é o número de variáveis independentes\", \"label-fundamentacao-03-descricao-identificacao-dados-mercado\": \"Identificação dos dados de mercado\", \"label-fundamentacao-03-grau-apresentacao-informacoes-relativas-caracteristicas-conferidas\": \"Apresentação de informações relativas a todos os dados e variáveis analisados na modelagem, com foto e características conferidas pelo autor do laudo\", \"label-fundamentacao-03-grau-apresentacao-informacoes-relativas\": \"Apresentação de informações relativas a todos os dados e variáveis analisados na modelagem\", \"label-fundamentacao-03-grau-apresentacao-informacoes-utilizadas-modelo\": \"Apresentação de informações relativas aos dados e variáveis efetivamente utilizados no modelo\", \"label-fundamentacao-04-descricao-extrapolacao\": \"Extrapolação\", \"label-fundamentacao-04-grau-nao-admitida\": \"Não admitida\", \"label-fundamentacao-04-admitida-apenas-referida-variavel\": \"Admitida para apenas uma variável, desde que: a) as medidas das características do imóvel avaliando não sejam superiores a 100% do limite amostral superior, nem inferiores à metade do limite amostral inferior, b) o valor estimado não ultrapasse 15% do valor calculado no limite da fronteira amostral, para a referida variável\", \"label-fundamentacao-04-admitida-apenas-referidas-variaveis-simutaneamente-modulo\": \"Admitida, desde que: a) as medidas das características do imóvel avaliando não sejam superiores a 100% do limite amostral superior, nem inferiores à metade do limite amostral inferior, b) o valor estimado não ultrapasse 20% do valor calculado no limite da fronteira amostral, para as referidas variáveis, de per si e simultaneamente, e em módulo\", \"label-fundamentacao-05-nivel-significancia-teste-bicaldal\": \"Nível significância (somatório do valor das duas caudas) máximo para a rejeição da hipótese nula de cada regressor (teste bicaldal)\", \"label-fundamentacao-05-grau-dez-porcento\": \"10%\", \"label-fundamentacao-05-grau-vinte-porcento\": \"20%\", \"label-fundamentacao-05-grau-trinta-porcento\": \"30%\", \"label-fundamentacao-06-nivel-significancia-teste-snedecor\": \"Nível significância máximo admitido para a rejeição da hipótese nula do modelo através do teste F de Snedecor\", \"label-fundamentacao-06-um-porcento\": \"1%\", \"label-fundamentacao-dois-porcento\": \"2%\", \"label-fundamentacao-cinco-porcento\": \"5%\", \"label-enquadramento-laudo-fundamentacao-pontos-minimos\": \"Pontos mínimos\", \"label-enquadramento-laudo-fundamentacao-dezesseis\": \"16\", \"label-enquadramento-laudo-fundamentacao-dez\": \"10\", \"label-enquadramento-laudo-fundamentacao-seis\": \"6\", \"label-titulo-confirmacao\": \"Confirmar\", \"label-enquadramento-laudo-fundamentacao-itens-obrigatorios\": \"Itens obrigatórios ao grau correspondente\", \"label-enquadramento-laudo-fundamentacao-grau-iii-demais-minimo-grau-ii\": \"2, 4, 5 e 6 no Grau III e os demais no mínimo no Grau II\", \"label-enquadramento-laudo-fundamentacao-grau-ii-demais-minimo-grau-i\": \"2, 4, 5 e 6 no Grau II e os demais no mínimo no Grau I\", \"label-enquadramento-laudo-fundamentacao-todos-minimo-grau-i\": \"Todos, o mínimo no Grau I\", \"label-enquadramento-laudo-precisao-amplitude-intervalo-confianca\": \"Amplitude do intervalo de confiança de 80% em torno da estimativa de tendência central\", \"label-elaborar-laudo\": \"Elaborar Laudo\", \"label-elaborar-relatorio\": \"Elaborar Relatório\", \"label-data\": \"Data\", \"label-registro-profissional\": \"Registro Profissional\", \"label-rip\": \"RIP\", \"rip-spiunet\": \"RIP SPIUnet\", \"label-nao-sei-rip\": \"Não sei o RIP\", \"label-imovel\": \"Imóvel\", \"valor-cub-depreciado-m2\": \"Valor do CUB Depreciado (m²)\", \"label-estado-conservacao\": \"Estado de Conservação\", \"label-dados-avaliacao\": \"Dados Avaliação\", \"label-normativos\": \"Normativos Utilizados\", \"label-norma\": \"Norma\", \"label-editar-norma\": \"Editar norma\", \"label-excluir-norma\": \"Excluir norma\", \"label-descricao-outros\":\"Descrição outros\", \"label-especificar-outra-metodologia\":\"Especificar outra metodologia\", \"label-metodologia\":\"Metodologia\", \"label-orgao\":\"Órgão\", \"label-unidade\":\"Unidade\", \"label-subunidade\":\"Subunidade\", \"label-avaliacao-terreno\":\"Avaliação do Terreno\", \"label-quantificacao-custo-benfeitorias\":\"Quantificação de Custo das Benfeitorias\", \"label-descricao-outros-infraestrutura-urbana\":\"Descrição de Outras Infraestrutura Urbana\", \"label-descricao-outros-servicos-comunitarios\":\"Descrição de Outros Serviços Comunitários\", \"label-escola\":\"Escola\", \"label-transporte-coletivo\":\"Transporte Coletivo\", \"label-area-lazer\":\"Área de Lazer\", \"label-posto-saude\":\"Posto de Saúde\", \"label-comercio\":\"Comércio\", \"label-seguranca\":\"Segurança\", \"label-rede-bancaria\":\"Rede Bancária\", \"label-outros\":\"Outros\", \"label-editar\":\"Editar\", \"label-excluir\":\"Excluir\", \"label-nivel-logradouro\":\"Cota em relação ao nível do logradouro\", \"label-observacao-complementacao\":\"Observação/complementação\", \"label-posicao\":\"Posição\", \"label-zoneamento\":\"Zoneamento\", \"label-tipo-de-projeto\":\"Tipo de Projeto\", \"label-vagas-deficientes-acessiveis\":\"Há vagas próximas dos acessos de circulação de pedestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.\", \"label-acessos-interior-acessiveis\":\"Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.\", \"label-banheiro-acessivel\":\"Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.\", \"label-elevador-acessivel\":\"Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.\", \"label-possui-documento\":\"Possui este documento?\", \"label-possui\":\"Possui?\", \"label-data-emissao\":\"Data de emissão\", \"label-data-validade\":\"Data de validade\", \"label-link-fonte\":\"Link da Fonte\", \"label-fonte\":\"Fonte\", \"label-execucao-vistoria\":\"Execução da Vistoria\", \"label-editar-valor\":\"Editar valor\", \"label-adicionar-valor\":\"Adicionar valor\", \"label-fonte-valor-benfeitorias\":\"Fonte do Valor das Benfeitorias\", \"label-fonte-valor-terreno\":\"Fonte do Valor do Terreno\", \"label-variaveis\":\"Variáveis\", \"label-variavel\":\"Variável\", \"label-variavel-utilizada-modelo\":\"Variável utilizada no modelo?\", \"label-dado\":\"Dado\", \"label-dados-mercado\":\"Dados de mercado\", \"label-utilizada\":\"Utilizada\", \"label-atributos-avaliando\":\"Atributos do Avaliando\", \"label-dados-vermelho-necessitam-complemento\":\"* Dados em vermelho necessitam de complementação\", \"label-registrar-valor-edificacao\":\"Registrar Valor Edificação\", \"label-remover-dado-complementar\":\"Deseja remover este dado complementar?\", \"label-dado-complementar\":\"Dado complementar \", \"label-descricao-orcamento-metodologia\":\"Descrição do Orçamento/Metodologia\", \"label-descricao-bdi-utilizado\":\"Descrição do BDI Utilizado\", \"label-descricao-depreciacao\":\"Descrição da Depreciação\", \"label-descricao-depreciacao-calculada\":\"Descrição da Depreciação Calculada\", \"label-descricao-depreciacao-arbitrada\":\"Descrição da Depreciação Arbitrada\", \"label-custo-total\":\"Custo total\", \"label-taxa-bdi\":\"Taxa BDI (%)\", \"label-taxa-depreciacao\":\"Taxa Depreciação (%)\", \"label-taxa-utilizada-orcamento\":\"Taxa utilizada no orçamento\", \"label-informacoes-contabilidade\":\"Informações para Contabilidade\", \"label-benfeitoria\":\"Benfeitoria\", \"label-estimativa-custo-direto\":\"Estimativa do custo direto\", \"label-estimativa-custo-direto-grau-3\":\"Pela elaboração de orçamento, no mínimo sintético\", \"label-estimativa-custo-direto-grau-2\":\"Pela utilização de custo unitário básico para projeto semelhante ao projeto padrão\", \"label-estimativa-custo-direto-grau-1\":\"Pela utilização de custo unitário básico para projeto diferente do projeto padrão, com os devidos ajustes\", \"label-bdi\":\"BDI\", \"label-bdi-grau-3\":\"Calculado\", \"label-bdi-grau-2\":\"Justificado\", \"label-bdi-grau-1\":\"Arbitrado\", \"label-depreciacao-fisica\":\"Depreciação Física\", \"label-depreciacao-fisica-grau-3\":\"Calculada por lavantamento do custo de recuperação do bem, para deixá-lo no estado de novo ou casos de bens novos ou projetos hipotéticos\", \"label-depreciacao-fisica-grau-2\":\"Calculada por métodos técnicos consagrados, considerando-se idade, vida útil e estado de conservação\", \"label-depreciacao-fisica-grau-1\":\"Arbitrada\", \"label-pontos-minimos\":\"Pontos Mínimos\", \"label-itens-obrigatorios-grau-correspondente\":\"Itens obrigatórios no grau correspondente\", \"label-enquadramento-grau-3\":\"1, com os demais no mínimo no Grau II\", \"label-enquadramento-grau-2\":\"1 e 2, no mínimo no Grau II\", \"label-enquadramento-grau-1\":\"Todos, no mínimo no Grau I\", \"label-area-equivalente\":\"Área Equivalente (m²)\", \"label-calcular-area-equivalentee\":\"Calcular Área Equivalente\", \"label-cub-padrao\":\"CUB Padrão\", \"label-custo-construcao\":\"Custo de Construção por m²\", \"label-estimativa\":\"Estimativa\", \"label-orcamento-elevadores\":\"Orçamento de Elevadores\", \"label-orcamento-instalacoes-especiais\":\"Orçamento de Instalações Especiais\", \"label-orcamento-fundacoes-especiais\":\"Orçamento de Fundações Especiais\", \"label-orcamento-fundacoes-diretas\":\"Orçamento de Fundações Diretas\", \"label-elevadores\":\"Elevadores\", \"label-instalacoes-especiais\":\"Instalações Especiais\", \"label-fundacoes-especiais\":\"Fundações Especiais\", \"label-fundacoes-diretas\":\"Fundações Diretas\", \"label-calcular-area-equivalente\":\"Calcular Área Equivalente\", \"label-cub-area-padrao\":\"CUB Área Padrão\", \"label-cub-padrao-difente\":\"CUB de Padrão Diferente\", \"label-area-construida-padrao-ap\":\"Área Construída Padrão (Ap)\", \"label-cub-area-padrao-diferente-aq\":\"Área Construída de Padrão Diferente (Aq)\", \"label-area-equivalente-construcao\":\"Área Equivalente de Construção (S)\", \"label-area-equivalente-calculada\":\"Área Equivalente Calculada\", \"label-cub-selecionado\":\"Cub Selecionada\", \"label-razao-entre-cubs\":\"Razão entre CUBs (P)\", \"label-detalhar\":\"Detalhar\", \"label-bdi-considerado-custo-reproducao\":\"BDI já considerado no custo de reprodução\", \"label-descricao-fator-comercializacao\":\"Descrição do Fator de Comercialização\", \"label-fc\":\"FC\", \"label-estimativa-valor-terreno\":\"Estimativa do valor do terreno\", \"label-estimativa-valor-terreno-grau-3\":\"Grau III de fundamentação do método comparativo ou involutivo\", \"label-estimativa-valor-terreno-grau-2\":\"Grau II de fundamentação do método comparativo ou involutivo\", \"label-estimativa-valor-terreno-grau-1\":\"Grau I de fundamentação do método comparativo ou involutivo\", \"label-estimativa-custos-reedicao\":\"Estimativas dos custos de reedição\", \"label-estimativa-custos-reedicao-grau-3\":\"Grau III de fundamentação no método da qualificação do custo\", \"label-estimativa-custos-reedicao-grau-2\":\"Grau II de fundamentação no método da qualificação do custo\", \"label-estimativa-custos-reedicao-grau-1\":\"Grau I de fundamentação no método da qualificação do custo\", \"label-fator-comercializacao\":\"Fator de comercialização\", \"label-fator-comercializacao-grau-3\":\"Inferido em Mercado Semelhante\", \"label-fator-comercializacao-grau-2\":\"Justificado\", \"label-fator-comercializacao-grau-1\":\"Arbitrado\", \"label-enquadramento-grau-3-resultado-avaliacao\":\"1 e 2, com o 3 no mínimo no Grau II\", \"label-nome-outra-variavel\":\"Nome Outra Variável\", \"label-valor-benfeitoria\":\"Valor da Benfeitoria (R$)\", \"label-valor-unidade-autonoma\":\"Valor da Unidade Autônoma (R$)\", \"label-descricao-estimativa\":\"Descrição da Estimativa\", \"label-descricao-fonte\":\"Descrição da Fonte\", \"label-amostra\":\"Amostra\", \"label-numero-documento-sei\":\"Número do Documento SEI\", \"label-n-documento-sei\":\"N° do Documento SEI\", \"label-descricao-metodologia-utilizada\":\"Descrição da Metodologia Utilizada\", \"label-descricao-area-diferente-padrao-construtivo\":\"Descrição da Área Diferente do Padrão Construtivo\", \"label-coeficiente-calculado\":\"Coeficiente calculado (P)\", \"label-coeficiente\":\"Coeficiente\", \"label-coeficiente-p\":\"Coeficiente (P)\", \"label-informar\":\"Informar\", \"label-calcular\":\"Calcular\", \"label-coeficiente-calculo-area-equivalente\":\"Coeficiente para cálculo das áreas equivalentes\", \"label-calculado-com-cub\":\"Calculado com CUB\", \"label-modelo\":\"Modelo\", \"label-valor-unitario\":\"Valor Unitário\", \"label-area-imovel\":\"Área Imóvel (m²)\", \"label-lote-paradigma\":\"Lote Paradigma\", \"label-valor-unitario-adotado\":\"Valor Unitário Adotado (m²)\", \"label-despesas-com-urbanizacao\":\"Despesas com Urbanização\", \"label-despesas-unitaria-com-urbanizacao\":\"Despesas Unitárias com Urbanização\", \"label-encerramento\":\"Encerramento\", \"label-valor-total-gleba\":\"Valor total da Gleba\", \"label-valor-m\":\"Valor do m²\", \"label-descricao-metodologia\":\"Descrição da Metodologia\", \"label-perdas-arruamento-lazer-areas-verdes\":\"Perdas em arruamento, lazer e áreas verdes (decimal)\", \"label-lucro-empreendedor\":\"Lucro do Empreendedor\", \"label-assinatura-nota-tecnica\":\"Assinatura da Nota Técnica\", \"label-assinatura-revalidacao\":\"Assinatura de Revalidação\", \"label-n-inscricao\":\"N° de Inscrição\", \"label-n-processo\":\"N° de Processo\", \"label-forma-incorporacao\":\"Forma de Incorporação\", \"label-valor-encargo\":\"Valor de Encargo (R$)\", \"label-data-final-encargo\":\"Data Final do Encargo\", \"label-direito-adquirido\":\"Direito Adquirido/Incorporado\", \"label-doacao-com-encargo\":\"Doação com encargo?\", \"label-outro-contrato-pessoal\":\"Outro Contrato Pessoal\", \"label-situacao-incorporacao\":\"Situação da Incorporação\", \"label-fundamento\":\"Fundamento\", \"label-zona-area-interesse-social\":\"Zona/Área de interesse social (ZEIS)\", \"label-situado-em-area-seguranca\":\"Situado em área de segurança\", \"label-area-non-aedificandi\":\"Área Non Aedificandi\", \"label-tombado-como-patrimonio-historico\":\"Tomabado como patrimônio Histórico, Artístico e/ou Cultural\", \"label-outra-restricao-uso-ocupacao\":\"Outra restrição de uso/ocupação\", \"label-taxa-ocupacao\":\"Taxa Ocupação\", \"label-possui-etiqueta-conservacao-energia\":\"Possui Etiqueta Nacional de Conservação de Energia (ENCE)\", \"label-tipo-parcial\":\"Tipo de Parcial\", \"label-nivel\":\"Nível\", \"label-avaliacao-excluida-com-sucesso\":\"Avaliação excluída com sucesso\", \"label-laudo-excluido-com-sucesso\":\"Laudo excluído com sucesso\", \"label-relatorio-excluido-com-sucesso\":\"Relatório excluído com sucesso\", \"label-pendencia-excluida-com-sucesso\":\"Pendência excluída com sucesso\", \"label-excluir-laudo\":\"Excluir laudo\", \"label-excluir-pendencia\":\"Excluir Pendência\", \"label-excluir-relatorio\":\"Excluir Relatório\", \"label-excluir-laudo-locacao\":\"Excluir Laudo de Locação\", \"label-remover-rascunho\":\"Remover Rascunho\", \"label-utilizar-documento-sei\": \"Utilizar Documento SEI\", \"label-enviar-documento-para-sei\": \"Enviar documento para o SEI\", \"label-criar-novo-processo-sei\": \"Criar novo processo no SEI\", \"label-utilizar-processo-existente-sei\": \"Utilizar processo existente no SEI\", \"label-numero-processo-sei\": \"Número do Processo SEI\", \"label-sincrornizar-laudo\": \"Sincronizar Avaliação \", \"label-laudo-sincronizado\": \"Avaliação sincronizada com o SPIUNET\", \"label-laudo-nao-sincronizado\": \"Avaliação não sincronizada com o SPIUNET\", \"label-nao-sincronizavel\": \"Avaliação não sincronizável com o SPIUNET\", \"label-laudo-migrado\": \"Dados de avaliação migrados do SPIUNet\", \"mensagem-sucesso-dados-gravados\": \"Dados gravados com sucesso.\", \"mensagem-sucesso-dados-salvos\": \"Dados salvos com sucesso.\", \"mensagem-sucesso-rascunho-removido\": \"Rascunho Laudo Avaliação removido com sucesso.\", \"mensagem-erro-pendencia-de-validacao-usuario\": \"A pendência não pode ser validada pelo mesmo usuário que registrou a ação.\", \"mensagem-assinatura-registrada-sucesso\": \"Assinatura registrada com sucesso.\", \"mensagem-confirmar-cancelar-laudo-relrorio\" :\"Após cancelar o laudo, este não poderá mais ser ativado. Deseja continuar?\", \"mensagem-exclusao-registro-aplicada-cadastro-imovel-confirm\": \"A exclusão desse registro será aplicada no cadastro do imóvel. Deseja continuar?\", \"mensagem-inclusao-registro-aplicada-cadastro-imovel-confirm\": \"A inclusão desse registro será aplicada no cadastro do imóvel. Deseja continuar?\", \"mensagem-cep-invalido\": \"O CEP informado não é válido.\", \"mensagem-confirmar-deseja-remover-motivo\": \"Deseja remover este motivo?\", \"mensagem-confirmar-deseja-remover-fator\": \"Deseja remover este fator?\", \"mensagem-rip-invalido\": \"RIP inválido\", \"mensagem-data-posterior-data-atual\": \"A data da assinatura não pode ser posterior a data atual.\", \"mensagem-data-nao-pode-posterior-data-atual\": \"A data não pode ser posterior a data atual.\", \"mensagem-cpf-invalido\": \"CPF inválido.\", \"mensagem-responsavel-tecnico-invalido\": \"Responsável técnico inválido.\", \"mensagem-responsavel-tecnico-existe\": \"Responsável técnico já existe.\", \"mensagem-arquivo-formato-invalido\": \"Arquivo inválido.\", \"mensagem-arquivo-formato-incompativel-modelo-variaveis\": \"Arquivo incompatível com o modelo de variáveis.\", \"mensagem-documento-formato-invalido\": \"Documento inválido.\", \"mensagem-arquivo-invalido\": \"Arquivo inválido\", \"mensagem-campos-obrigatorios\": \"Necessário preencher os campos obrigatórios\", \"mensagem-dados-nao-serao-salvos-confirm\": \"Os dados não salvos serão perdidos. Deseja continuar?\", \"mensagem-remover-responsavel-tecnico-confirm\": \"Deseja remover este item?\", \"mensagem-nenhum-registro-encontrado\": \"Nenhum registro encontrado\", \"mensagem-selecione-imovel\": \"Selecione um imóvel.\", \"mensagem-data-invalida\": \"Data inválida\", \"mensagem-necessario-informar-responsavel-tecnico\": \"Necessário informar responsável técnico.\", \"mensagem-necessario-informar-crea-responsavel\": \"Necessário informar o CREA do responsável.\", \"mensagem-necessario-informar-homologador\": \"Necessário informar um Homologador.\", \"mensagem-norma-existe\": \"Essa norma já foi adicionada\", \"mensagem-existe-laudo-avaliacao\": \"Existe Laudo de Avaliação válido ou passível de revalidação cadastrado para este imóvel.\", \"mensagem-existe-laudo-locacao\": \"Existe Laudo Locação/Arrendamento válido ou passível de revalidação cadastrado para este imóvel.\", \"mensagem-existe-relatorio-valor-referencia\": \"Existe Relatório de Valor de Referência válido cadastrado para este imóvel.\", \"mensagem-periodo-documento-invalido\": \"Período do documento inválido.\", \"mensagem-metodo-avaliacao-invalido\": \"O método de avaliação selecionado não é permitido para imóveis que não possuem benfeitorias.\", \"mensagem-periodo-vistoria-invalido\": \"Período de vistoria inválido\", \"mensagem-incluir-fonte-valor-terreno\": \"Deseja incluir a avaliação para fonte do valor do terreno?\", \"mensagem-valor-mininimo-maior-medio\": \"O valor mínimo não pode ser maior que o valor médio.\", \"mensagem-valor-mininimo-maior-maximo\": \"O valor mínimo não pode ser maior que o valor máximo.\", \"mensagem-valor-médio-maior-maximo\": \"O valor médio não pode ser maior que o valor máximo.\", \"mensagem-valor-adotado-maior-maximo-menor-minimo\": \"O valor adotado não pode ser maior que o valor máximo e não pode ser menor que o valor mínimo.\", \"mensagem-data-posterior-data-atual-arquivo\": \"A data do arquivo não pode ser posterior a data atual.\", \"mensagem-variavel-adicionada-sucesso\": \"Variável adicionada com sucesso\", \"mensagem-variavel-alterada-sucesso\": \"Variável alterada com sucesso\", \"mensagem-arquivo-inserido\": \"O nome ou arquivo informado para o laudo não pode ser o mesmo de arquivos já inseridos.\", \"mensagem-extensao-nao-permitida\": \"Extensão não permitida.\", \"mensagem-nenhum-valor-encontrado-para-cub\": \"Nenhum valor encontrado para o CUB selecionado.\", \"mensagem-area-construida-padrao-invalida\": \"Área Construída Padrão (Aq) inválida.\", \"mensagem-area-construida-padrao-diferente-invalida\": \"Área Construída Padrão Diferente (Aq) inválida.\", \"mensagem-area-equivalente-construcao-diferente\": \"Área Equivalente de Construção (S) diferente da área da benfeitoria.\", \"button-download-planilha-depreciacao-heidecke\": \"Fazer Download da Planilha de Depreciação de Heidecke\", \"mensagem-adicionar-itens-nao-previstos-cub\": \"Adicionar itens não previstos no CUB\", \"mensagem-adicionar-bdi\": \"Adicionar BDI\", \"mensagem-remover-esta-variavel-confirm\": \"Deseja remover está variável?\", \"mensagem-remover-esta-variavel-com-dados-mercado-confirm\": \"Já existem dados de mercado incluídos que utilizam essa variável. Todas os dados de mercado serão removidos. Deseja continuar?\", \"mensagem-variavel-nome-informada-ja-foi-adicionada\": \"Uma variável com o nome informado já foi adicionada.\", \"mensagem-remover-dado-mercado\": \"Deseja remover este dado de mercado?\", \"mensagem-necessario-informar-numero-documento-sei\": \"Necessário informar um número de documento SEI\", \"mensagem-documento-sei-adicionado-sucesso\": \"Documento SEI adicionado com sucesso.\", \"mensagem-numero-documento-sei-informado-ja-adicionado-avaliação\": \"O nº do documento informado já foi adicionado na avaliação\", \"mensagem-validar-valor-imovel\": \"O valor da área construída e do terreno não corresponde ao valor do imóvel.\", \"mensagem-dominio-pleno-superior-valor-imovel\": \"Valor do Domínio pleno maior que o valor de Avaliação. Não é possível estimar valor do terreno.\", \"mensagem-cub-area-padrao-igual-informado\": \"O CUB de Área Padrão não pode ser igual à um CUB de Padrão Diferente informado.\", \"mensagem-avaliacao-imovel-nao-cadastrada\": \"Este imóvel não possui avaliação deste tipo cadastrada. Deseja cadastrar?\", \"mensagem-responsavel-tecnico-nao-usuario-sistema\": \"O responsável informado não é um usuário do sistema.\", \"mensagem-tipo-relatorio-imoveis-sem-edificacao\": \"Este tipo de relatório só é permitido para imóveis sem edificação.\", \"menssagem-numero-processo-sei-invalido\": \"Número do processo SEI não informado ou inválido. Por favor, insira um número válido.\", \"mensagem-erro-verificar-laudo-revalidacao\": \"Não foi possível verificar a possibilidade de revalidação dos relatórios. Por favor, tente novamente mais tarde.\", \"menssagem-limite-revalidacoes\": \"Limite de revalidações atingido. Não é possível revalidar o laudo.\", \"mensagem-tipo-laudo-avaliacao-desconhecido\": \"Esta função não pode ser usada com esse tipo de avaliação.\", \"mensagem-usuario-nao-possui-permissao-acessar-servico-consulta-pessoa\": \"CPF não possui cadastro no Avaliação e o usuário logado não tem permissão para acessar os dados na Receita Federal.\", \"mensagem-erro-cancelar-laudo\": \"Não é possível cancelar a única avaliação vigente ou válida do imóvel. Reavalie o imóvel e, após isso, refaça a operação de cancelamento.\", \"menu-busca-inteligente\": \"Busca inteligente\", \"menu-voce-esta-aqui\": \"Você está aqui\", \"menu-sua-sessao-expira\": \"Sua sessão expira em\", \"placeholder-data\": \"Data\", \"placeholder-data-matricula\": \"Data da Matrícula\", \"placeholder-data-registro\": \"Data da Registro\", \"placeholder-data-Inicio\": \"Início\", \"placeholder-data-inicial\": \"Data inicial\", \"placeholder-data-final\": \"Data final\", \"radio-estimar-valores-cub\": \"Estimar valores CUB\", \"radio-estimar-valores-terreno\": \"Estimar valores Terreno\", \"radio-valores-laudo\": \"Informar valores do Laudo\", \"radio-outra-fonte\": \"Outra fonte\", \"radio-cub\": \"CUB\", \"radio-pvg-spu\": \"PVG/SPU\", \"radio-valor-municipio\": \"Valor venal/município\", \"radio-incra\": \"Referência INCRA\", \"span-imoveis\": \"Imóveis\", \"span-preenchimento-obrigatorio\": \"Campo de preenchimento obrigatório\", \"title-avaliacao-terreno-referencia-valor-venal-municipio\": \"Avaliação de Terreno - referência Valor Venal/Município\", \"title-avaliacao-terreno-pvg-spu\": \"Avaliação de Terreno - referência PVG/SPU\", \"title-valor-dominio-pleno\": \"Valor de Domínio Pleno\", \"title-avaliacao-terreno-referencia-incra\": \"Avaliação de Terreno Rural - referência INCRA\", \"title-consultar-avaliacao\": \"Consultar Avaliação\", \"title-dados-aquisicao-onerosa\": \"Dados de Aquisição Onerosa\", \"title-fotos-do-imovel\": \"Fotos do Imóvel\", \"title-laudo-avaliacao-relatorio-referencia\": \"Laudo de Avaliação/Relatório de Referência\", \"title-localizacao-imovel\": \"Localização do imóvel\", \"title-motivo-da-alteracao\": \"Motivo da Alteração\", \"title-valor\": \"Valor R$\", \"title-valor-terreno\": \"Valor do Terreno (R$)\", \"title-unidade\": \"Unidade\", \"title-detalhar-mapa-imovel\": \"Detalhar Mapa do Imóvel\", \"title-cancelar-laudo-avaliacao\": \"Cancelar Laudo de Avaliação\", \"title-cancelar-referencia_valor_venal_municipio\": \"Cancelar referência Valor Venal/Município\", \"title-cancelar-referencia_incra\": \"Cancelar referência INCRA\", \"title-cancelar-pvg_spu\": \"Cancelar PVG/SPU\", \"title-consultar_laudo_relatorio\": \"Consultar Laudo/Relatório\", \"title-responsavel-destinatario\":\"Responsável/Destinatário\", \"title-uso-efetivo\":\"Uso Efetivo\", \"title-uso-potencial\": \"Uso Potêncial\", \"title-dados-utilizacao\": \"Dados da Utilização\", \"title-localização-imovel\": \"Localização do Imóvel\", \"title-detalhes-imovel\": \"Detalhes do Imóvel\", \"title-matricula-transcricao\": \"Matrícula/Transcrição\", \"title-documento-cartorial\": \"Documentos Cartoriais\", \"title-descricao-regiao\": \"Descrição da Região\", \"title-proprietario\": \"Proprietário\", \"title-detalhes-terreno\": \"Detalhes do Terreno\", \"title-descricao-caracteristicas-fisica-terreno\": \"Descrição das Caracteristicas Físicas do Terreno\", \"title-documentos\": \"Documentos\", \"title-benfeitoria\": \"Benfeitoria\", \"title-edificação-edificil-multipavimento\": \"E3 - Edificação/Edificio Multipavimentos\", \"title-descricao-benfeitoria\": \"Descricao Benfeitoria\", \"title-complementar-campo-futebol\": \"C1 - Complementar/Campo de Futebol\", \"title-proprietario-imovel\": \"Proprietário do Imóvel\", \"title-parametros-urbanisticos\": \"Parâmetros Urbanísticos\", \"title-terceiros\": \"Terceiros\", \"title-dados-edificacao\": \"Dados da Edificação\", \"title-descricao-unidade-autonoma\": \"Descrição da Unidade Autônoma\", \"title-condicao-acessibilidade\": \"Condições de Acessibilidade\", \"title-detalhes-unidade-autonoma\": \"Detalhes da Unidade Autônoma\", \"title-condicoes-acessibilidade\": \"Condições de Acessibilidade\", \"title-dados-da-avaliacao\": \"Dados da Avaliação\", \"title-resultados-da-avaliacao\": \"Resultados da Avaliação\", \"title-informar-valor-terreno-benfeitoria\": \"Informar Valor de Terreno e Benfeitoria\", \"title-pressupostos\": \"Pressupostos\", \"title-ressalvas-fatores\": \"Ressalvas e fatores limitantes\", \"title-linearidade\": \"Linearidade\", \"title-normalidade\": \"Normalidade\", \"title-homocedasticidade\": \"Homocedasticidade\", \"title-verificacao-autocorrelacao\": \"Verificação da Autocorrelação\", \"title-colinearidade-multicolinearidade\": \"Colinearidade ou multicolinearidade\", \"title-pontos-influenciantes\": \"Pontos influenciantes ou outliers\", \"title-diagnostico-de-mercado\": \"Diagnóstico de mercado\", \"title-liquidez-do-imovel\": \"Liquidez do imóvel\", \"title-descricao-das-variaveis\": \"Descrição das variáveis\", \"title-descricao-avaliacao\": \"Descrição da Avaliação\", \"title-atributos-do-avaliando\": \"Atributos do avaliando\", \"title-pesquisa-de-mercado\": \"Período de coleta de dados de mercado\", \"title-pressupostos-modelo\": \"Pressupostos do modelo de\", \"title-ferramenta-utilizada\": \"Ferramenta utilizada\", \"title-valores\": \"Valores\", \"title-moeda\": \"Moeda\", \"title-pressuposto\": \"Pressuposto\", \"title-valor-medio\": \"Valor Médio\", \"title-valor-minimo\": \"Valor Mínimo\", \"title-valor-maximo\": \"Valor Máximo\", \"title-valor-adotado\": \"Valor Adotado\", \"title-valor-calculado\": \"Valor Calculado\", \"title-outro-valor\": \"Outro Valor\", \"title-outro-valor-adotado\": \"Outro Valor Adotado\", \"title-justificativa-do-valor-adotado\": \"Justificativa do valor adotado\", \"title-esquadramento\": \"Enquadramento\", \"title-checkbox1-laudo-mobilidade\": \"Apresentação do laudo na modalidade completa.\", \"title-checkbox2-analise-modelo\": \"Apresentação da análise do modelo no laudo de avaliação, com a verificação da coerência do comportamento da variação das variáveis em relação ao mercado, bem como suas elasticidades em torno do ponto de estimação.\", \"title-checkbox3-idenfificacao-completa\": \"Identificação completa nos endereços dos dados de mercado usados no modelo, bem como das fontes de informação.\", \"title-checkbox4-estimativa\": \"Adoção da estimativa de tendência central\", \"title-fundamentacao-nbr\": \"Fundamentação - NBR 14653-2\", \"title-item\": \"Item\", \"title-descricao\": \"Descrição\", \"title-grau-3\": \"Grau III\", \"title-grau-2\": \"Grau II\", \"title-grau-1\": \"Grau I\", \"title-pontos-obtidos\": \"Pontos Obtidos\", \"title-caracterizacao-imovel\": \"Caracterização do imóvel avaliando\", \"title-completa-variaveis-analisadas\": \"Completa quanto a todas as variáveis analisadas\", \"title-completa-variaveis-utilizadas\": \"Completa quanto a todas as variáveis utilizadas no modelo\", \"title-adocao-situacao-paradigma\": \"Adoção de situação paradigma\", \"title-quantidade-minima-mercado\": \"Quantidade mínima de dados de mercado efetivamente utilizados\", \"title-numero-variaveis-independentes-6\": \"6(k+1), onde k é o número de variáveis independentes\", \"title-numero-variaveis-indepententes-4\": \"4(k+1), onde k é o número de variáveis independentes\", \"title-numero-variaveis-indepententes-3\": \"3(k+1), onde k é o número de variáveis independentes\", \"title-identificacao-dados\": \"Identificação dos dados de mercado\", \"title-apresentacao-informacoes-relativas\": \"Apresentação de informações relativas a todos os dados e variáveis analisados na modelagem, com foto e características conferidas pelo autor do laudo\", \"title-apresntacao-informacoes-analisadas\": \"Apresentação de informações relativas a todos os dados e variáveis analisados na modelagem\", \"title-apresentacao-relativa-\": \"Apresentação de informações relativas aos dados e variáveis efetivamente utilizados no modelo\", \"title-extrapolacao\": \"Extrapolação\", \"title-nao-admitida\": \"Não admitida\", \"title-adimitida-apenas-uma-variavel\": \"Admitida para apenas uma variável, desde que: a) As medidas das caracteristicas do imóvel avaliando não sejam superiores a 100% do limite amostral superior, nem inferiores à metade do limite amostral inferior. b) O valor estimado não ultrapasse 15% do valor calculado no limite da fronteira amostral, para a referida variável\", \"title-admitida-desde-que\": \"Admitida, desde que: a) As medidas das caracteristicas do imóvel avaliando não sejam superiores a 100% do limite amostral superior, nem inferiores à metade do limite amostral inferior; b) O valor estimado não ultrapasse 20% do valor calculado no limite da fronteira amostral, para as referidas variáveis, de per si e simultaneamente, e em módulo\", \"title-nivel-de-significancia\": \"Nível de significancia (Somatório do valor das duas caudas) máximos para rejeição da hipótese nula de cada regressor (Teste bicaudal)\", \"title-nivel-de-significancia-maximo\": \"Nível de significancia máximo admitido para a rejeição da hipótese nula do modelo através do teste F de Snedecor\", \"title-pontuacao-obtida\": \"Pontuação obtida\", \"title-justificativa-nao-atingir-grau\": \"Justificativa para não atingimento do Grau mínimo dos itens\", \"title-enquadrament-laudo-segundo-grau\": \"Enquadramento do Laudo segundo seu grau de Fundamentação - NBR 14653-2\", \"title-graus\": \"Graus\", \"title-pontos-minimos\": \"Pontos mínimos\", \"title-itens-obrigatorios\": \"itens obrigatórios ao grau correspondente\", \"title-2-4-5-6-grau3\": \"2, 4, 5 e 6 no Grau lll e os demais no mínimo no Grau ll\", \"title-2-4-5-6-grau2\": \"2, 4, 5 e 6 no Grau ll e os demais no mínimo no Grau l\", \"title-todos-minimo-grau1\": \"Todos, o mínimo no Grau l\", \"title-grau-fundamentacao-laudo\": \"Grau de fundamentação do Laudo de Avaliação\", \"title-enquadramento-laudo-segundo-grau-precisao\": \"Enquadramento do Laudo segundo seu grau de Precisão - NBR 14653-2\", \"title-amplitude-intervalo-confianca\": \"Amplitude do intervalo de confiança de 80% em torno da estimativa de tendência central\", \"title-grau-precisao-laudo-avaliacao\": \"Grau de precisão do Laudo de Avaliação\", \"title-justificativa-nao-enquadramento-laudo\": \"Justificativa para o não enquadramento do laudo de avaliação quanto à precisão com base no diagnóstico de mercado\", \"title-encerramento\": \"Encerramento\", \"title-anexos\": \"Anexos\", \"title-nome\": \"Nome\", \"title-data\": \"Data\", \"title-selecionar-arquivo\": \"Selecionar Arquivo\", \"title-adicionar\": \"Adicionar\", \"title-arquivo\": \"Arquivo\", \"title-arquivos\": \"Arquivos\", \"title-retornar\": \"Retornar\", \"title-fechar\": \"Fechar\", \"title-salvar-rascunho\": \"Salvar Rascunho\", \"title-concluir\": \"Concluir\", \"title-dados-vistoria\": \"Dados da Vistoria\", \"title-fotos-vistoria-imovel\": \"Fotos da vistoria do imóvel\", \"title-variaveis-amostra\": \"Variáveis e Amostra\", \"title-valor-m2\": \"Valor do m² (R$)\", \"title-periodo-avaliacao\": \"Período da Avaliação\", \"title-modelo-adotado\": \"Modelo adotado(função estimativa)\", \"title-dados-laudo-avaliacao\": \"Dados do Laudo de Avaliação\", \"title-dados-laudo-avaliacao-locacao\": \"Dados do Laudo de Avaliação - Locação/Arrendamento\", \"title-dados-relatorio-valor-referencia\": \"Dados do Relatório do Valor de Referência\", \"title-anexos-da-avaliacao\": \"Anexos da Avaliação\", \"title-custo-reproducao\": \"Custo de Reprodução\", \"title-bdi\": \"BDI\", \"title-depreciacao\": \"Depreciação\", \"title-valor-benfeitorias\":\"Valor das Benfeitorias\", \"title-grau-fundamentacao-metodo-quantificacao-custos-benfeitorias\":\"Grau de Fundamentação do Método da Quantificação de Custos de Benfeitorias\", \"title-grau-fundamentacao-metodo-quantificacao-custos\":\"Grau de fundamentação no método da quantificação de custos:\", \"title-grau-fundamentacao-metodo-evolutivo\":\"Grau de fundamentação do Método Evolutivo\", \"title-enquadramento-grau-fundamentacao\":\"Enquadramento do Grau de Fundamentação\", \"title-3\":\"III\", \"title-2\":\"II\", \"title-1\":\"I\", \"title-area\":\"Área\", \"title-estimacao-custos\":\"Estimação de Custos\", \"title-custo-construcao\":\"Custo de Construção\", \"title-fator-comercializacao\":\"Fator de Comercialização\", \"title-valor-imovel\":\"Valor do Imóvel\", \"title-grau-fundamentacao-laudo-avaliacao\":\"Grau de fundamentação do Laudo de Avaliação\", \"title-localizacao-amostra\":\"Localização da Amostra\", \"title-helio-caires\":\"Hélio de Caires\", \"title-jose-barbagelata\":\"José Barbagelata\", \"title-oscar-olave\":\"Oscar Olave\", \"title-resultado-avaliacao\":\"Resultado da Avaliação\", \"title-restricao-uso-ocupacao\":\"Restrição de Uso/Ocupação\", \"tr-acao\": \"Ação\", \"tr-acoes\": \"Ações\", \"tr-area-terreno-fracao\": \"Área do Terreno / Fração Ideal\", \"tr-arquivo\": \"Arquivo\", \"tr-cep\":\"CEP\", \"tr-cub\": \"CUB\", \"tr-data-aquisicao\": \"Data da aquisição\", \"tr-data-da-assinatura\": \"Data da Assinatura\", \"tr-data-assinatura\": \"Data de Assinatura\", \"tr-data-cadastramento\": \"Data de cadastramento\", \"tr-data-inicio-periodo\": \"Data de Início do Período\", \"tr-revalidado\": \"Revalidado\", \"tr-data-ultima-atualizacao\": \"Data da Última Atualização\", \"tr-detalhes\": \"Detalhes\", \"tr-descricao\": \"Descrição\", \"tr-estado-conservacao\": \"Estado de Conservação\", \"tr-endereco\":\"Endereço\", \"tr-id\": \"ID\", \"tr-idade-aparente\": \"Idade Aparente (Ano)\", \"tr-imovel\": \"Imóvel\", \"tr-imovel-nao-possui-benfeitoria\": \"O imóvel não possui benfeitorias\", \"tr-matricula-trascricao\": \"Matrícula/Trascrição\", \"tr-nome-do-trecho\": \"Nome do Trecho\", \"tr-periodo\": \"Período\", \"tr-prazo-validade\": \"Prazo de Validade\", \"tr-periodo-avaliacao\": \"Período de Avaliação\", \"tr-fator-corretivo-total\": \"Fator Corretivo Total\", \"tr-proprietario-titular\": \"Proprietário/Titular\", \"tr-responsavel-tecnico\": \"Responsável Técnico\", \"tr-rip\":\"RIP\", \"tr-situacao\": \"Situação\", \"tr-tipo\": \"Tipo\", \"tr-valido-para-alienacao\": \"Válido para alienação\", \"tr-valor\": \"Valor\", \"tr-valor-unitario\": \"Valor unitário em R$\", \"tr-valor-aquisicao-onerosa\": \"Valor da aquisição onerosa\", \"tr-valor-estimado\": \"Valor Estimado\", \"tr-valor-do-imovel\": \"Valor do Imóvel (R$)\", \"tr-valor-do-imovel-laudo-avaliacao\": \"Valor do Imóvel Laudo de Avaliação\", \"tr-valor-do-imovel-relatorio-valor-referencia\": \"Valor do Imóvel Relatório de Valor de Referência\", \"tr-valor-do-terreno\": \"Valor do Terreno\", \"tr-valor-do-terreno-pvg-incra\": \"Valor do Terreno PVG/INCRA\", \"tr-valor-do-terreno-pvg-spu\": \"Valor do Terreno PVG/SPU\", \"tr-valor-do-terreno-pvg-municipio\": \"Valor do Terreno PVG/Munícipio\", \"tr-vida-util\": \"Vida Útil\", \"tr-ultima-atualizacao\": \"Última Atualização\", \"tr-numero-laudo-relatorio\": \"Nº do Laudo/Relatório\", \"tr-metodo-avaliacao\":\"Método de Avaliação\", \"tr-valor-rs\":\"Valor (R$)\", \"tr-cartorio\":\"Cartório\", \"tr-nome\":\"Nome\", \"tr-data-matricula\":\"Data Matrícula\", \"tr-data-registro\":\"Data Registro\", \"tr-data\":\"Data\", \"tr-localizacao\": \"Localização\", \"tr-cpf-cnpj\": \"CPF/CNPJ\", \"tr-nome-razao-social\": \"Nome/Razão Social\", \"tr-data-vistoria\": \"Data da Vistoria\", \"tr-data-inicio-vistoria\": \"Data Início da Vistoria\", \"tr-data-fim-vistoria\": \"Data Fim da Vistoria\", \"tr-selecionar-item\": \"Selecione um item abaixo para a inclusão das fotos\", \"tr-selecionar-item-fotos\": \"Selecione um item abaixo para escolher as fotos do cadastro do imóvel para o Laudo/Relatório de Avaliação\", \"tr-fotos-selecionadas\": \"Usar as fotos selecionadas\", \"tr-matricula-transcricao\":\"Matrícula/Transcrição\", \"tr-proprietario\":\"Proprietário\", \"tr-selecionar\":\"Selecionar\", \"tr-fonte\":\"Fonte\", \"tr-elaborado-por\":\"Elaborado por\", \"vida-util-ano\": \"Vida Útil (Ano)\", \"valor-estimado-cub-m2\": \"Valor Estimado CUB (m²)\", \"valores-terreno-benfeitoria\": \"Valores de Terreno e Benfeitoria\", \"label-adicionar-fatores\": \"Adicionar Fatores\", \"label-fator\": \"Fator\", \"label-justificativa\": \"Justificativa\", \"title-fatores\": \"Fatores\", \"label-excluir-fator\": \"Excluir Fator\", \"label-editar-fator\": \"Editar Fator\", \"label-anotacoes-observacoes-vistoria\": \"Anotações/Observações da Vistoria\", \"label-incluir-arquivo-modelo\": \"Incluir arquivo do modelo\", \"arquivo-pdf-excede-tamanho\": \"Arquivo excede o tamanho limite de 15MB. Recarregue a página.\", \"decisao-validacao-aceito\": \"Aceito\", \"decisao-validacao-nao-aceito\": \"Não Aceito\", \"label-alerta-obrigatorio-responsavel-tecnico\": \"Para avaliação elaborada por <strong>Órgão/Entidade</strong>, é <strong>obrigatório</strong> que o responsável técnico seja um <strong>servidor da unidade gestora do imóvel</strong>.\" }");
$templateCache.put("scripts-avaliacao/app/views/acesso.negado.tmpl.html","<div class=msg-acesso-negado><span>Ops!!! Você não tem acesso a essa funcionalidade!</span><div layout=row flex=100><md-icon class=icon-acesso-negado ng-bind=\"\'cancel\'\" alt=\"Acesso Negado\"><md-tooltip>Acesso Negado</md-tooltip></md-icon></div></div>");
$templateCache.put("scripts-avaliacao/app/views/header.html","<!-- Barra de navageção --><md-toolbar layout=row class=md-whiteframe-z3 ng-controller=\"MenuController as ul\"><!-- hide-gt-sm --><md-button class=menu ng-click=ul.toggleMenu() aria-label=\"Menu da Aplicação\"><ng-md-icon icon=menu style=fill:#fff size=38></ng-md-icon></md-button><div id=nome-sistema layout=row flex=40 layout-align=\"space-around none\" class=elemento-barra-navegacao><div class=titulo-app style=color:white><span>SPUnet</span></div><div class=divisor-vertical hide show-gt-sm style=color:white></div><span hide show-gt-sm class=\"subtitulo-app md-caption\" style=color:white>Secretaria de Patrimônio da União</span><div flex></div></div><div flex></div><div layout=row flex=75 layout-align=\"end center\"><a ui-sref=INTEGRADOR.HOME class=posicao-itens-barra-menu><ng-md-icon ui-sref=home icon=home style=fill:#fff size=35 alt=Home><md-tooltip md-direction=bottom>Página inicial</md-tooltip></ng-md-icon></a><ng-md-icon icon=notifications class=posicao-itens-barra-menu size=35 alt=Notificações style=\"fill: #fff\"><md-tooltip md-direction=bottom>Notificações</md-tooltip></ng-md-icon><ng-md-icon icon=help class=posicao-itens-barra-menu size=35 alt=Ajuda style=\"fill: #fff\"><md-tooltip md-direction=bottom>Ajuda</md-tooltip></ng-md-icon><a href=/api/saml/logout><ng-md-icon icon=do_not_disturb class=posicao-itens-barra-menu size=35 alt=Notificações style=\"fill: #fff\"><md-tooltip md-direction=bottom>Logout</md-tooltip></ng-md-icon></a><md-button class=\"md-fab md-primary md-hue-2\" ng-click=$mdOpenMenu($event) aria-label=Configurações><md-tooltip md-direction=left>Configurações da aplicação</md-tooltip><md-icon>person</md-icon></md-button></div></md-toolbar><md-toolbar layout=row class=md-whiteframe-z3 style=\"min-height: 34px !important; background: #38813C !important\"><div flex=60 ncy-breadcrumb></div><!--<div flex=\"60\" style=\"text-align: right;\" >\n        <md-input-container style=\"width: 40%; text-align: center;margin-bottom: 1%;margin-top: 1%; font-size: large;\" class=\"favoritas\" layout-align=\"end center\">\n            <md-select style=\"padding-top: 0px;padding-bottom: 0px;\" ng-model=\"teste\" aria-label=\"tetsts\" placeholder=\"Funções favoritas\">\n                <md-option ng-value=\"favoritas\">Funções favoritas</md-option>\n            </md-select>\n        </md-input-container>\n    </div>\n    --><div flex layout=row layout-align=\"end center\"><contador-sessao></contador-sessao></div><!-- Container Menu e Conteúdo --></md-toolbar><!-- MENU --><menu>");
$templateCache.put("scripts-avaliacao/app/views/main.view.tmpl.html","<div flex layout=column><!-- Conteúdo Principal --><div style=\"overflow:auto; height: 100% !important\"><div ng-include=\"\'scripts-avaliacao/app/views/header.html\'\"></div><md-content flex layout=column style=\"height: calc(100% - 47px);padding: 10px!important\"><div ui-view></div></md-content><footer class=\"footer p-b-0\" style=\"font-size: 14px\">Secretaria de Patrimônio da União - SPU | Esplanadas dos Ministérios - Bloco C -2° Andar Brasília-DF</footer></div></div>");
$templateCache.put("scripts-avaliacao/app/views/servico.indisponivel.tmpl.html","<div class=msg-servico-indisponivel><span>Serviço Indisponível. Aguarde alguns minutos e refaça sua operação.</span><div layout=row flex=100><md-icon class=icon-servico-indisponivel ng-bind=\"\'cancel\'\" alt=\"Serviço Indisponível\"><md-tooltip>Serviço Indisponível no momento</md-tooltip></md-icon></div></div>");
$templateCache.put("scripts-avaliacao/pages/breadcrumb/breadcrumb.html","<div id=breadcrumb><ul class=breadcrumb><span class=voce-esta-aqui>VOCÊ ESTÁ AQUI:</span><li class=migalha-pao ng-repeat=\"step in steps | limitTo:(steps.length-1)\"><a href={{step.ncyBreadcrumbLink}} ng-bind-html=step.ncyBreadcrumbLabel></a></li><li class=migalha-pao ng-repeat=\"step in steps | limitTo:-1\" class=active><span ng-bind-html=step.ncyBreadcrumbLabel></span></li></ul></div>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogCalculoAreaEquivalente.tmpl.html","<md-dialog aria-label=\"Cálculo de Área Equivalente\" flex=30 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Cálculo de Área Equivalente</span><md-button class=md-icon-button ng-click=vm.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-align=\"center start\"><div flex><code>S = Ap + &#8721 <sub>i</sub> <sup>n</sup> (Aq<sub>i</sub> - P<sub>i</sub>)<br><br>Onde:<br><b>S</b> é a área equivalente de construção<br><b>Ap</b> é a área construída padrão<br><b>Aq<sub>i</sub></b> é a área construída de padrão diferente<br><b>P<sub>i</sub></b> é o percentual correspondente à razão entre o custo estimado da área de padrão diferente e a área padrão, de acordo com os limites estabelecidos na ABNT NBR 12721.<br></code></div></div></div></md-dialog-content><md-dialog-actions layout=row><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=vm.cancelModal()><label>{{\'button-fechar\' | translate}}</label></md-button></div></md-dialog-actions></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogCalculoModelosGleba.tmpl.html","<md-dialog aria-label=\"Cálculo de Área Equivalente\" flex=50 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" ng-show=\"vm.modeloAvaliacaoGleba == \'HELIO_CAIRES\'\" flex>{{\'title-helio-caires\' | translate}}</span> <span style=\"font-size: 18px\" ng-show=\"vm.modeloAvaliacaoGleba == \'JOSE_BARBAGELATA\'\" flex>{{\'title-jose-barbagelata\' | translate}}</span> <span style=\"font-size: 18px\" ng-show=\"vm.modeloAvaliacaoGleba == \'OSCAR_OLAVE\'\" flex>{{\'title-oscar-olave\' | translate}}</span><md-button class=md-icon-button ng-click=vm.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-align=\"center start\"><div flex><code ng-if=\"vm.modeloAvaliacaoGleba == \'HELIO_CAIRES\'\"><div layout=column><img class=img-formula-gu-center src=assets/img/gu_formula_helio.png></div>Onde:<br>X - valor total da gleba bruta (R$);<br>S - área total da gleba (m²);<br>q - valor unitário médio de lotes (R$/m2);<br>D<sub>u</sub> - despesas com urbanização (R$).<br></code> <code ng-if=\"vm.modeloAvaliacaoGleba == \'JOSE_BARBAGELATA\'\"><div layout=column><img class=img-formula-gu-center src=assets/img/gu_formula_jose.png></div>Onde:<br>X - valor total da gleba bruta (R$);<br>S - área total da gleba (m²);<br>K - perdas em arruamento, lazer e áreas verdes (decimal);<br>q - valor unitário médio de lotes (R$/m2);<br>p - despesas unitárias de urbanização.<br></code> <code ng-if=\"vm.modeloAvaliacaoGleba == \'OSCAR_OLAVE\'\"><div layout=column><img class=img-formula-gu-center src=assets/img/gu_formula_oscar.png></div>Onde:<br>X - valor total da gleba bruta (R$);<br>D<sub>r</sub> - despesas totais com urbanização e loteamento (R$).<br>L - lucro percentual do empreendedor (%);<br>S - área total da gleba (m²);<br>K - perdas em arruamento, lazer e áreas verdes (decimal);<br>q - valor unitário médio de lotes (R$/m2);<br></code></div></div></div></md-dialog-content><md-dialog-actions layout=row><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=vm.cancelModal()><label>{{\'button-fechar\' | translate}}</label></md-button></div></md-dialog-actions></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogCancelarLaudoAvaliacao.tmpl.html","<md-dialog aria-label={{titulo}} flex=60 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><h3>{{titulo}}</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=fecharDialogCancelar()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><form name=formularioCancelarLaudoAvaliacao class=p-t-20><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-data\' | translate}}:</label><input type=text ng-disabled=true ng-model=dataMostrar></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-nome-responsavel\' | translate}}:</label><input type=text ng-disabled=true ng-model=nomeResponsavel.responsavelCancelamento></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50><label>{{\'label-motivo\' | translate}}:</label><md-select id=motivoCancelamento ng-model=selecioneMotivo name=\"Motivo Cancelamento\" required><md-option ng-repeat=\"(k,v) in motivosCancelar\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div class=m-b-30 layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-offset=5><label>{{\'label-justificativa\' | translate}}</label><textarea rows=4 cols=50 ng-model=justificativa maxlength=500 required></textarea><span style=\"color: #cc0000; float: right\">{{0 + justificativa.length}} / 500</span></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=fecharDialogCancelar()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=cancelar(formularioCancelarLaudoAvaliacao);><label>{{\'button-confirmar\' | translate}}</label></md-button></div></div></div></form></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogDepreciacaoHeidecke.tmpl.html","<md-dialog aria-label=\"Depreciação de Heidecke\" flex=60 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Depreciação de Heidecke</span><md-button class=md-icon-button ng-click=vm.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'tr-estado-conservacao\' | translate}}</th><th md-column>{{\'tr-descricao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in vm.listaEstadosConservacao | orderBy: item.nome\"><td md-cell><label ng-bind=item.nome></label></td><td md-cell style=\"text-align: justify !important\"><label style=\"text-align: justify\" ng-bind=item.descricao></label></td></tr></tbody></table></md-table-container></div></div></div></md-dialog-content><md-dialog-actions layout=row><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=vm.downloadPlanilha()><label>{{\'button-download-planilha-depreciacao-heidecke\' | translate}}</label></md-button><md-button class=\"md-primary md-raised\" type=button ng-click=vm.cancelModal()><label>{{\'button-fechar\' | translate}}</label></md-button></div></md-dialog-actions></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogDetalhesBenfeitoriaDepreciacao.tmpl.html","<md-dialog aria-label=Informações flex=60 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><h3>Informações</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=fecharDialog()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><md-dialog-content><div class=m-t-20 layout-align=\"start center\" flex=100 layout=row><label>Idade Aparente: <b>{{(benfeitoria.idadeAparente * 100 / benfeitoria.vidaUtil).toFixed(0)}}%</b></label></div><div layout-align=\"start center\" flex=100 layout=row><label>Ocorrencia de novo Evento: <b>{{getCuteName(benfeitoria.benfeitoriaImovel.tipoLaudo)}}</b></label></div><div layout-align=\"start center\" flex=100 layout=row><label>Inclusão da Edificação: <b></b></label></div><div layout-align=\"start center\" flex=100 layout=row><label>Alteração da data de edificação: <b></b></label></div></md-dialog-content></md-dialog>s");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogDetalhesCubBenfeitoria.tmpl.html","<md-dialog aria-label=\"{{\'label-detalhes-do-cub\' | translate}}\" flex=60 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><h3>{{\'label-detalhes-do-cub\' | translate}}</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=fecharDialogDetalhesValorCub()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><md-dialog-content><div class=m-t-20 layout-align=\"start center\" flex=100 layout=row><label>{{\'vida-util-ano\' | translate}}: <b>{{benfeitoria.vidaUtil}}</b></label></div><div layout-align=\"start center\" flex=100 layout=row><label>{{\'idade-aparente-ano\' | translate}}: <b>{{benfeitoria.idadeAparente}}</b></label></div><div layout-align=\"start center\" flex=100 layout=row><label>{{\'label-estado-conservacao\' | translate}}: <b>{{benfeitoria.estadoConservacao.nome}}</b></label></div><div class=m-b-20 layout-align=\"start center\" flex=100 layout=row ng-show=\"benfeitoria.valorCubDepreciado != null\"><label>{{\'valor-cub-depreciado-m2\' | translate}}: <b ng-bind=\"(benfeitoria.valorCubDepreciado | currency).replace(\'R$\',\'\')\"></b></label></div><div flex class=marginTopHomologadorNome><div layout=column><md-content flex ng-show=\"benfeitoria.estimativas.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th class=alinhamentoTextoCentro md-column>{{\'label-motivo\' | translate}}</th><th class=alinhamentoTextoCentro md-column>{{\'label-porcentagem\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"estimativa in benfeitoria.estimativas\"><td class=alinhamentoTextoCentro md-cell><label ng-bind=listaMotivosEstimativa[estimativa.motivo]></label></td><td class=alinhamentoTextoCentro md-cell><label ng-bind=\"(estimativa.porcentagem | percentage)\"></label></td></tr></tbody></table></md-table-container></md-content></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogDetalhesCubUnidadeAutonoma.tmpl.html","<md-dialog aria-label=\"{{\'label-detalhes-do-cub\' | translate}}\" flex=60 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><h3>{{\'label-detalhes-do-cub\' | translate}}</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=fecharDialogDetalhesValorCub()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><md-dialog-content><md-content flex><div id=formData layout=column><div class=m-t-20 layout-align=\"start center\" flex=100 layout=row><label>{{\'vida-util-ano\' | translate}}: <b>{{unidadeAutonoma.vidaUtil}}</b></label></div><div layout-align=\"start center\" flex=100 layout=row><label>{{\'idade-aparente-ano\' | translate}}: <b>{{unidadeAutonoma.idadeAparente}}</b></label></div><div layout-align=\"start center\" flex=100 layout=row><label>{{\'label-estado-conservacao\' | translate}}: <b>{{unidadeAutonoma.estadoConservacao.nome}}</b></label></div><div class=m-b-20 layout-align=\"start center\" flex=100 layout=row ng-show=\"unidadeAutonoma.valorCubDepreciado != null\"><label>{{\'valor-cub-depreciado-m2\' | translate}}: <b ng-bind=\"(unidadeAutonoma.valorCubDepreciado | currency).replace(\'R$\',\'\')\"></b></label></div><div flex class=marginTopHomologadorNome><div layout=column><md-content flex ng-show=\"unidadeAutonoma.estimativas.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th class=alinhamentoTextoCentro md-column>{{\'label-motivo\' | translate}}</th><th class=alinhamentoTextoCentro md-column>{{\'label-porcentagem\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"estimativa in unidadeAutonoma.estimativas\"><td class=alinhamentoTextoCentro md-cell><label ng-bind=listaMotivosEstimativa[estimativa.motivo]></label></td><td class=alinhamentoTextoCentro md-cell><label ng-bind=\"(estimativa.porcentagem | percentage)\"></label></td></tr></tbody></table></md-table-container></md-content></div></div></div></md-content></md-dialog-content></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogDetalhesDadosAvaliacao.tmpl.html","<md-dialog aria-label=\"{{\'label-detalhes-do-cub\' | translate}}\" flex=60 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><h3>{{\'label-dados-avaliacao\' | translate}}</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=fecharDialogDetalhesDadosAvaliacao()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><md-dialog-content class=m-t-20><div class=m-b-10 layout=row flex ng-show=dadosImovelAvaliacao.valor><label flex=100><span class=negritoTexto>{{\'tr-valor-do-imovel\' |translate}}:</span> <span ng-bind=\"(dadosImovelAvaliacao.valor | currency).replace(\'R$\', \'\')\"></span></label></div><div class=m-b-10 layout=row flex ng-show=dadosImovelAvaliacao.dataAssinatura><label flex=100><span class=negritoTexto>{{\'tr-data-assinatura\' |translate}}:</span> <span ng-bind=dadosImovelAvaliacao.dataAssinatura></span></label></div><div class=m-b-10 layout=row flex ng-show=dadosImovelAvaliacao.tipoAvaliacao><label flex=100><span class=negritoTexto>{{\'tr-tipo\' |translate}}:</span> <span ng-bind=dadosImovelAvaliacao.tipoAvaliacao></span></label></div><div class=m-b-10 layout=row flex ng-show=dadosImovelAvaliacao.valorTerrenoPvgSpu><label flex=50><span class=negritoTexto>{{\'tr-valor-do-terreno-pvg-spu\' |translate}}:</span> <span ng-bind=\"(dadosImovelAvaliacao.valorTerrenoPvgSpu | currency).replace(\'R$\', \'\')\"></span></label><label flex=50><span class=negritoTexto>{{\'tr-data-ultima-atualizacao\' |translate}}:</span> <span ng-bind=\"dadosImovelAvaliacao.dataUltimaAtualizacaoPvgSpu | date: \'dd/MM/yyyy\' : \'-0300\'\"></span></label></div><div layout=row flex ng-show=dadosImovelAvaliacao.nomeTrechoPvgSpu><label flex=50><span class=negritoTexto>{{\'tr-nome-do-trecho\' |translate}}:</span> <span ng-bind=dadosImovelAvaliacao.nomeTrechoPvgSpu></span></label></div><div class=m-b-10 layout=row flex ng-show=dadosImovelAvaliacao.valorTerrenoPvgMunicipio><label flex=50><span class=negritoTexto>{{\'tr-valor-do-terreno-pvg-municipio\' |translate}}:</span> <span ng-bind=\"(dadosImovelAvaliacao.valorTerrenoPvgMunicipio | currency).replace(\'R$\', \'\')\"></span></label><label flex=50><span class=negritoTexto>{{\'tr-data-inicio-periodo\' |translate}}:</span> <span ng-bind=\"dadosImovelAvaliacao.dataInicioPeriodoPvgMunicipio | date: \'dd/MM/yyyy\' : \'-0300\'\"></span></label></div><div class=m-b-10 layout=row flex ng-show=dadosImovelAvaliacao.valorTerrenoPvgIncra><label flex=50><span class=negritoTexto>{{\'tr-valor-do-terreno-pvg-incra\' |translate}}:</span> <span ng-bind=\"(dadosImovelAvaliacao.valorTerrenoPvgIncra | currency).replace(\'R$\', \'\')\"></span></label><label flex=50><span class=negritoTexto>{{\'tr-data-inicio-periodo\' |translate}}:</span> <span ng-bind=\"dadosImovelAvaliacao.dataInicioPeriodoPvgIncra | date: \'dd/MM/yyyy\' : \'-0300\'\"></span></label></div><div class=m-b-10 layout=row flex ng-show=false><label flex=50><span class=negritoTexto>{{\'tr-valor-aquisicao-onerosa\' |translate}}:</span> <span ng-bind=\"(dadosImovelAvaliacao.valorTerrenoPvgIncra | currency).replace(\'R$\', \'\')\"></span></label><label flex=50><span class=negritoTexto>{{\'tr-data-aquisicao\' |translate}}:</span> <span ng-bind=\"dadosImovelAvaliacao.dataInicioPeriodoPvgIncra | date: \'dd/MM/yyyy\' : \'-0300\'\"></span></label></div></md-dialog-content><md-dialog-actions layout=row><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=fecharDialogDetalhesDadosAvaliacao()><label>{{\'button-fechar\' | translate}}</label></md-button></div></div></md-dialog-actions></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogDetalhesDescricaoVariavel.tmpl.html","<md-dialog aria-label=Informações flex=60 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><h3>Variável: {{vm.variavelModal.nome}}</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=vm.cancelModal()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><md-dialog-content><div class=\"m-t-10 m-b-10\" layout-align=\"start start\" flex=100 layout=row><label><b>Descricão:</b></label></div><div layout-align=\"start Start\" flex=100 layout=row><label style=\"word-wrap: break-word; width: 100%\">{{vm.variavelModal.descricao}}</label></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogDetalhesEvento.tmpl.html","<md-dialog aria-label=Informações flex=60 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><h3>Dados do Evento</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=fecharDialog()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><md-dialog-content><div class=m-t-20 layout-align=\"start center\" flex=100 layout=row><label>Valor do Terreno: <b>{{((evento.valorTerreno | currency) || \'--------\').replace(\'R$\', \'\')}}</b></label></div><div layout-align=\"start center\" flex=100 layout=row><label>Variação do Valor do Domínio Pleno: <b>{{((evento.variacaoValorTerreno | currency) || \'--------\').replace(\'R$\', \'\')}}</b></label></div><div ng-repeat=\"benfeitoria in evento.benfeitorias\"><div layout-align=\"start center\" flex=100 layout=row><label>Valor da Benfeitoria [{{benfeitoria.codigo}}]: <b>{{((benfeitoria.valor | currency) || \'--------\').replace(\'R$\', \'\')}}</b></label></div><div layout-align=\"start center\" flex=100 layout=row><label>Variação do CUB: <b>{{((benfeitoria.variacaoValor | currency) || \'--------\').replace(\'R$\', \'\')}}</b></label></div></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogFotosDetalharImovel.tmpl.html","<md-dialog aria-label=\"{{\'title-fotos-do-imovel\' | translate}}\" flex=50 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><h3>{{\'title-fotos-do-imovel\' | translate}}</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=fechar()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><md-dialog-content><div class=md-dialog-content><fotos-avaliacao-imovel lista-imagens-imovel=listaFotosImovel></fotos-avaliacao-imovel></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogGerarPlanilhaAmostra.tmpl.html","<md-dialog aria-label=\"Depreciação de Heidecke\" flex=60 id=su-avaliacao><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>{{\'label-orientacoes-preenchimento-arquivo\' | translate}}</span><md-button class=md-icon-button ng-click=vm.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'tr-coluna\' | translate}}</th><th md-column>{{\'tr-caracteres-validos\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>Área do Terreno(m²)</label></td><td md-cell style=\"text-align: justify !important\"><label style=\"text-align: justify\">Numéricos, \'.\' e \',\'</label></td></tr><tr md-row><td md-cell><label>Área Construída(m²)</label></td><td md-cell style=\"text-align: justify !important\"><label style=\"text-align: justify\">Numéricos, \'.\' e \',\'</label></td></tr><tr md-row><td md-cell><label>Classificação (T/O)</label></td><td md-cell style=\"text-align: justify !important\"><label style=\"text-align: justify\">\'T\' e \'O\', independente se minúsculo ou maiúsculo. Apenas um caractere.</label></td></tr><tr md-row><td md-cell><label>Valor</label></td><td md-cell style=\"text-align: justify !important\"><label style=\"text-align: justify\">Numéricos, \'.\' e \',\'</label></td></tr><tr md-row><td md-cell><label>Classificação quanto ao valor de locação (T/O)</label></td><td md-cell style=\"text-align: justify !important\"><label style=\"text-align: justify\">\'T\' e \'O\', independente se minúsculo ou maiúsculo. Apenas um caractere.</label></td></tr><tr md-row><td md-cell><label>Valor para locação</label></td><td md-cell style=\"text-align: justify !important\"><label style=\"text-align: justify\">Numéricos, \'.\' e \',\'</label></td></tr><tr md-row><td md-cell><label>Informante/fonte</label></td><td md-cell style=\"text-align: justify !important\"><label style=\"text-align: justify\">Alfanumérico</label></td></tr><tr md-row><td md-cell><label>CEP</label></td><td md-cell style=\"text-align: justify !important\"><label style=\"text-align: justify\">Numéricos e \'-\'</label></td></tr><tr md-row><td md-cell><label>Dados Complementares 1..N</label></td><td md-cell style=\"text-align: justify !important\"><label style=\"text-align: justify\">Alfanumérico</label></td></tr></tbody></table></md-table-container></div></div></div></md-dialog-content><md-dialog-actions layout=row><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=vm.cancelModal()><label>{{\'button-fechar\' | translate}}</label></md-button></div></md-dialog-actions></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogMapaAmostra.tmpl.html","<md-dialog aria-label=\"{{\'title-localizacao-amostra\' | translate}}\" flex=70 id=su-avaliacao><form ng-cloak><md-toolbar><div class=md-toolbar-tools><h3>{{\'title-localizacao-amostra\' | translate}}</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=fecharDialogMapaAmostra()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><div style=\"margin: 10px\"><md-dialog-content flex><toaster-container></toaster-container><div layout=row layout-xs=column flex-sm=column ng-if=carregarMapaModal><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><ci-mapa-avaliacao mostrar-amostras=true showminimapa=true heightminimapa=50 widthminimapa=50 bt-marcar=false bt-camada=true bt-remover=false visualizar=true lista-amostras=amostrasDialog latitude=amostrasDialog[0].latitude longitude=amostrasDialog[0].longitude detalhar-amostra=true bloquear-mapa=false bt-selecionar=false></ci-mapa-avaliacao></md-input-container></div></md-dialog-content></div></form></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogMapaDetalharImovel.tmpl.html","<md-dialog aria-label=\"{{\'title-detalhar-mapa-imovel\' | translate}}\" flex=60 id=su-avaliacao style=\"height: 500px\"><form ng-cloak style=\"height: 500px\"><md-toolbar><div class=md-toolbar-tools><h3>{{\'title-detalhar-mapa-imovel\' | translate}}</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=fecharDialogMapaDetalharImovel()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><div style=\"margin-left: 10px; margin-right: 10px\"><md-dialog-content flex><toaster-container></toaster-container><mapa-detalhar-imovel ponto-imovel=pontoImovel dados-imovel-avaliacao=dadosImovelAvaliacao benfeitorias=benfeitorias geometria-terreno=geometriaTerreno></mapa-detalhar-imovel></md-dialog-content></div></form></md-dialog>");
$templateCache.put("scripts-avaliacao/app/directives/partial/dialogMapaDetalharLocalizacoesImoveis.tmpl.html","<md-dialog aria-label=\"{{\'title-detalhar-mapa-imovel\' | translate}}\" flex=60 id=su-avaliacao style=\"height: 500px\"><form ng-cloak style=\"height: 500px\"><md-toolbar><div class=md-toolbar-tools><h3>{{\'title-detalhar-mapa-imovel\' | translate}}</h3><span flex></span> <a href=\"\" class=md-icon-button ng-click=fecharDialogMapaDetalharLocalizacoesImoveis()><ng-md-icon icon=close style=fill:#FFFFFF size=25 alt=\"Fechar janela\"><md-tooltip>{{\'button-fechar-janela\' | translate}}</md-tooltip></ng-md-icon></a></div></md-toolbar><div style=\"margin-left: 10px; margin-right: 10px\"><md-dialog-content flex><toaster-container></toaster-container><mapa-detalhar-localizacoes-imoveis lista-dados-imoveis-avaliacao=listaDadosImoveisAvaliacao></mapa-detalhar-localizacoes-imoveis></md-dialog-content></div></form></md-dialog>");
$templateCache.put("scripts-avaliacao/pages/amostra/language/pt-BR.json","{ \"button-recusar\": \"Recusar\", \"button-fechar\": \"Fechar\", \"button-confirmar-selecao\": \"Confirmar Seleção\", \"button-confirmar\": \"Confirmar\", \"button-adicionar\": \"Adicionar\", \"button-assinar\": \"Assinar\", \"button-cancelar\": \"Cancelar\", \"button-confirmar-cancelamento\": \"Confirmar Cancelamento\", \"button-confirmar-selecao-dados-mercado\": \"Utilizar Dados de Mercado Selecionados\", \"button-limpar\": \"Limpar\", \"button-retornar\": \"Retornar\", \"button-salvar-rascunho\": \"Salvar Rascunho\", \"label-dados-mercado\": \"Dados de mercado\", \"label-tipo\": \"Tipo\", \"label-classificacao\": \"Classificação\", \"label-classificacao-valor-locacao\": \"Classificação quanto ao valor de locação\", \"label-area\": \"Área(m²)\", \"label-area-terreno\": \"Área do Terreno (m²)\", \"label-area-construida\": \"Área Construída (m²)\", \"label-valor-transacao\": \"Valor da transação (R$)\", \"label-valor-oferta\": \"Valor da Oferta (R$)\", \"label-valor\": \"Valor(R$)\", \"label-valor-transacao-locacao\": \"Valor da transação para locação (R$)\", \"label-valor-oferta-locacao\": \"Valor da Oferta para locação(R$)\", \"label-valor-locacao\": \"Valor de locação(R$)\", \"label-informante-fonte\": \"Informante/Fonte\", \"label-nao-sei-cep\": \"Não sei o CEP\", \"label-cep\": \"CEP\", \"label-logradouro\": \"Logradouro\", \"label-tipo-logradouro\": \"Tipo Logradouro\", \"label-municipio\": \"Município\", \"label-nome\": \"Nome\", \"label-numero\": \"Nº\", \"label-bairro-distrito\":\"Bairro/Distrito\", \"label-complemento\":\"Complemento\", \"label-uf\":\"UF\", \"label-localizacao\":\"Localização*\", \"label-codigo\":\"Código\", \"label-periodo-atualizacao\":\"Período de Atualização\", \"label-dados-mercado-ja-utilizado\":\"Dados de mercado já utilizados?\", \"label-utilizado\":\"Dados utilizado?\", \"label-apresentacao-consulta\":\"Apresentacao dos resultados da consulta*\", \"label-tabela\":\"Tabela\", \"label-mapa\":\"Mapa\", \"label-somente-dados-mercado-fotos\":\"Somente dados de mercado com foto\", \"label-somente-dados-mercado-fontes\":\"Somente dados de mercado com fonte\", \"label-somente-dados-mercado-area-entre\":\"Somente dados de mercado com área entre\", \"label-somente-dados-mercado-valor-entre\":\"Somente dados de mercado com valor entre\", \"label-cancelar\":\"Cancelar\", \"label-detalhar\":\"Visualizar\", \"label-incluir-dados-complementares\":\"Incluir dados complementares\", \"label-incluir-arquivos-complementares\":\"Incluir Arquivos complementares\", \"label-arquivos-complementares\": \"Arquivos Complementares\", \"label-dados-complementares\": \"Dados Complementares\", \"label-descricao-outro-dado-compelmentar\": \"Descrição do outro dado complementar\", \"label-situacao\": \"Situação\", \"label-orientacoes-preenchimento-arquivo\": \"Orientações para preenchimento do arquivo\", \"label-orientacoes\": \"orientações.\", \"label-numero-laudo-relatorio\": \"N° do Laudo/Relatório\", \"label-imovel-avaliado\": \"Imóvel Avaliado\", \"label-valor-imovel\": \"Valor do Imóvel\", \"label-total-registros-importados\": \"Total de registros importados\", \"label-responsavel\": \"Responsável\", \"label-registro-pendencia-preenchimento\": \"Registro com pendência de preenchimento\", \"label-data-upload\": \"Data do Upload\", \"label-dados-pendentes\": \"Dados de Pendentes\", \"label-dados-completos\": \"dados de Completos\", \"label-incluir-fotos\": \"Incluir Fotos\", \"button-exportar-planilha-dados-mercado\": \"Exportar planilha de dados de mercado\", \"button-gerar-planilha-dados-mercado\": \"Gerar Planilha de Dados de Mercado\", \"button-adicionar-registros-base-dados-mercado\": \"Adicionar registro à base de dados de mercado\", \"tr-data-atualizacao\":\"Data de Atualização\", \"tr-informante-fonte\":\"Informante/Fonte\", \"tr-codigo\":\"Código\", \"tr-foto\":\"Foto\", \"tr-coluna\":\"Coluna\", \"tr-caracteres-validos\":\"Caracteres Válidos\", \"placeholder-data-assinatura\": \"Data da assinatura\", \"placeholder-data\": \"Data\", \"title-dados-mercado\": \"Dados de Mercado\", \"title-inserir-dado-mercado\": \"Inserir Dado de Mercado\", \"title-detalhar-dado-mercado\": \"Detalhar Dado de Mercado\", \"title-editar-dado-mercado\": \"Editar Dado de Mercado\", \"title-cancelar-dados-mercado\": \"Cancelar Dados de Mercado\", \"title-consultar-dados-mercado\": \"Consultar Dados de Mercado\", \"title-selecionar-dados-mercado\": \"Selecionar Dados de Mercado\", \"title-fotos\": \"Fotos\", \"title-dados-cancelamento\": \"Dados do Cancelamento\", \"title-gerar-planilha-dados-mercado\": \"Gerar Planilha de Dados de Mercado\", \"title-upload-planilha-dados-mercado\": \"Upload Planilha de Dados de Mercado\", \"title-utilizacao\": \"Utilização\", \"title-consultar-upload-dados-mercado\": \"Consultar Upload de Dados de Mercado\", \"title-completar-informacoes-dados-mercado\": \"Complementar as Informações de Dados de Mercado\", \"mensagem-erro-importacao\": \"Foram encontradas inconsistências nas informações do arquivo selecionado. Os dados não podem ser importados. Verifique o relatório de inconsistências para mais informaçoes: \", \"mensagem-erro-importacao-cont\": \"Caso tenha dúvidas quanto ao preenchimento do arquivo, consulte as \", \"mensagem-dados-mercado-cancelado-sucesso\": \"Dado de mercado cancelado com sucesso\", \"mensagem-cep-invalido\": \"O CEP Informado não é válido.\", \"mensagem-arquivo-incompatível\": \"Arquivo incompatível\", \"mensagem-nenhum-dado-mercado-selecionada\": \"Necessário selecionar pelo menos um dado de mercado\", \"mensagem-dados-salvos-sucesso\": \"Dados salvos com sucesso\", \"mensagem-dados-mercado-adicionados-base\": \"Dados de Mercado adicionados à base.\", \"mensagem-registros-pendencia-preenchimento\": \"Registros com pendência de preenchimento não podem ser adicionados à base de dados de mercado.\", \"mensagem-sucesso-dados-gravados-amostra\": \"Dados gravados com sucesso. Código do Dado de Mercado: \", \"mensagem-dados-alterados-sucesso\": \"Dados alterados com sucesso\" }");
$templateCache.put("scripts-avaliacao/pages/amostra/views/adicionarVariavelAmostra.html","<form name=formularioDadosAmostra novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-dados-mercado\' | translate}}</h2></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100><label class=datepicker-label><label>{{\'label-utilizado\' | translate}}*</label></label></div></div><div layout=row class=m-b-20></div><div layout=row ng-if=\"variaveisAmostras && variaveisAmostras.length > 0\"><md-radio-group flex=20 class=tamanho-radio-button-avaliacao ng-model=variaveisAmostras[0].isAmostraUtilizada required name=\"{{nomeCampo}} - {{\'label-utilizado\' | translate}}\"><div layout=row><md-radio-button ng-repeat=\"(k,v) in listaCondicoes\" layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=v><div class=texto-preto>{{k == \'SIM\' ? \'Sim\' : \'Não\'}}</div></md-radio-button></div></md-radio-group></div><div layout=row ng-if=\"!variaveisAmostras || variaveisAmostras.length == 0\"><md-radio-group flex=20 class=tamanho-radio-button-avaliacao ng-model=avaliacao.variaveis[0].isAmostraUtilizada required name=\"{{nomeCampo}} - {{\'label-utilizado\' | translate}}\"><div layout=row><md-radio-button ng-repeat=\"(k,v) in listaCondicoes\" layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=v><div class=texto-preto>{{k == \'SIM\' ? \'Sim\' : \'Não\'}}</div></md-radio-button></div></md-radio-group></div><div layout=row layout-xs=column><md-input-container class=md-block flex=33><label>{{\'label-tipo\' | translate}}:</label><md-select ng-model=amostra.tipoAmostra ng-change=pesquisarTiposDadosComplementares(amostra.tipoAmostra) name=\"{{nomeCampo}} - Tipo Amostra\" required ng-disabled=\"detalharAmostra || (editarAmostra && !desbloquearAmostra)\"><md-option ng-repeat=\"elemento in listaTiposAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-area-terreno\' | translate}}</label><input type=text ng-model=amostra.areaTerreno name=\"{{nomeCampo}} - {{\'label-area-terreno\' | translate}}\" ui-number-mask=2 ng-blur=changeAreaTerreno(amostra.areaTerreno) ng-blur=changeValores(amostra.areaTerreno) ng-required=\"amostra.areaConstruida == null\" ng-disabled=detalharAmostra></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 ng-show=\"amostra.tipoAmostra != \'TERRENO\'\"><label>{{\'label-area-construida\' | translate}}</label><input type=text ng-model=amostra.areaConstruida name=\"{{nomeCampo}} - {{\'label-area-construida\' | translate}}\" ui-number-mask=2 ng-blur=changeAreaConstruida(amostra.areaConstruida) ng-required=\"amostra.areaTerreno == null\" ng-disabled=detalharAmostra></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33><label>{{\'label-classificacao\' | translate}}:</label><md-select ng-model=amostra.classificacaoAmostra ng-change=changeClassificacaoAmostra(amostra.classificacaoAmostra) name=\"{{nomeCampo}} - Classificação da Amostra\" ng-disabled=detalharAmostra><md-option ng-repeat=\"elemento in listaClassificacaoAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33><div layout=row><label ng-show=!amostra.classificacaoAmostra>{{\'label-valor\' | translate}}</label><label ng-show=amostra.classificacaoAmostra>{{(amostra.classificacaoAmostra == \'TRANSACAO\' ? \'label-valor-transacao\' : \'label-valor-oferta\') | translate}}</label></div><input type=text ng-blur=changeValorAmotra(amostra.valor) ng-model=amostra.valor name=\"{{nomeCampo}} - Valor\" ui-number-mask=2 ng-required=\"amostra.classificacaoAmostra != null\" ng-disabled=detalharAmostra></md-input-container><div flex=33><span ng-bind=valorPorExtenso></span></div></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33><label>{{\'label-classificacao-valor-locacao\' | translate}}:</label><md-select ng-model=amostra.classificacaoAmostraLocacao ng-change=changeClassificacaoAmostraLocacao(amostra.classificacaoAmostraLocacao) name=\"{{nomeCampo}} - Classificação da Amostra quanto ao valor de locação\" ng-disabled=detalharAmostra><md-option ng-repeat=\"elemento in listaClassificacaoAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33><div layout=row><label ng-show=!amostra.classificacaoAmostraLocacao>{{\'label-valor-locacao\' | translate}}</label><label ng-show=amostra.classificacaoAmostraLocacao>{{(amostra.classificacaoAmostraLocacao == \'OFERTA\' ? \'label-valor-oferta-locacao\' : \'label-valor-transacao-locacao\') | translate}}</label></div><input type=text ng-blur=changeValorAmotraLocacao(amostra.valorLocacao) ng-model=amostra.valorLocacao name=\"{{nomeCampo}} - Valor Locação\" ui-number-mask=2 ng-required=\"amostra.classificacaoAmostraLocacao != null\" ng-disabled=detalharAmostra></md-input-container><div flex=33><span ng-bind=valorLocacaoPorExtenso></span></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-informante-fonte\' | translate}}</label><textarea class=barraRolagemTextArea ng-model=amostra.informanteFonte ng-disabled=detalharAmostra ui-mask-placeholder-char=space required></textarea></md-input-container></div><div layout=row layout-xs=column class=m-b-10><md-checkbox class=md-primary ng-model=amostra.isDadosComplementares ng-change=limparDadosComplementares(amostra.isDadosComplementares); name=\"{{\'label-incluir-dados-complementares\' | translate}}\" ng-disabled=detalharAmostra><label class=text-black>{{\'label-incluir-dados-complementares\' | translate}}</label></md-checkbox></div><dados-complementares-amostra ng-if=amostra.isDadosComplementares dados-complementares=amostra.dadosComplementares detalhar-amostra=detalharAmostra gerar-planilha=false lista-tipos-dados-complementares=listaTiposDadosComplementares></dados-complementares-amostra><div layout=row layout-xs=column class=m-b-10><md-checkbox class=md-primary ng-model=amostra.isArquivosComplementares ng-change=limparArquivosComplementares(amostra.isArquivosComplementares); name=\"{{\'label-incluir-arquivos-complementares\' | translate}}\" ng-disabled=detalharAmostra><label class=text-black>{{\'label-incluir-arquivos-complementares\' | translate}}</label></md-checkbox></div><documento-laudo-avaliacao-padrao ng-if=amostra.isArquivosComplementares lista-documentos-laudo=amostra.arquivosComplementares campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_01\'\" detalhar-laudo-avaliacao=detalharAmostra titulo=\"\'label-arquivos-complementares\' | translate\"></documento-laudo-avaliacao-padrao><documento-fonte-amostra-padrao lista-documentos=amostra.listaDocumentoFonte detalhar-amostra=detalharAmostra titulo=\"\'Fonte\' | translate\"></documento-fonte-amostra-padrao><div layout=row ng-repeat=\"variavelAmostra in variaveisAmostras\" ng-if=\"variaveisAmostras && variaveisAmostras.length > 0\"><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block ng-if=\"variavelAmostra.variavel.tipoVariavel == \'TEXTO\'\"><label>{{variavelAmostra.variavel.nome == \'OUTRO\' ? variavelAmostra.variavel.nomeOutraVariavel : listaNomesVariavelCompleta[variavelAmostra.variavel.nome]}}:</label><input type=text ng-model=variavelAmostra.valor maxlength=50 ng-maxlength=50></md-input-container><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block ng-if=\"variavelAmostra.variavel.tipoVariavel != \'TEXTO\'\"><label>{{variavelAmostra.variavel.nome == \'OUTRO\' ? variavelAmostra.variavel.nomeOutraVariavel : listaNomesVariavelCompleta[variavelAmostra.variavel.nome]}}:</label><input ng-model=variavelAmostra.valor ui-number-mask=0 maxlength=50 ng-maxlength=50></md-input-container></div><div layout=row ng-repeat=\"variavel in avaliacao.variaveis\" ng-if=\"!variaveisAmostras || variaveisAmostras.length == 0\"><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block ng-if=\"variavel.tipoVariavel == \'TEXTO\'\"><label>{{variavel.nome == \'OUTRO\' ? variavel.nomeOutraVariavel : listaNomesVariavelCompleta[variavel.nome]}}:</label><input type=text ng-model=variavel.valor maxlength=50 ng-maxlength=50></md-input-container><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block ng-if=\"variavel.tipoVariavel != \'TEXTO\'\"><label>{{variavel.nome == \'OUTRO\' ? variavel.nomeOutraVariavel : listaNomesVariavelCompleta[variavel.nome]}}:</label><input ng-model=variavel.valor ui-number-mask=0 maxlength=50 ng-maxlength=50></md-input-container></div><documento-laudo-avaliacao-padrao lista-documentos-laudo=amostra.fotos campo-data=\"\'true\'\" tipo-foto=\"\'true\'\" detalhar-laudo-avaliacao=detalharAmostra titulo=\"\'title-fotos\' | translate\"></documento-laudo-avaliacao-padrao><div class=upload-arquivos ng-if=\"(detalharAmostra || editarAmostra) && paginationDadosUtilizacao.length > 0\"><div layout=row flex layout-align=\"start start\"><h3 class=subtitulo-pagina>{{\'title-utilizacao\' | translate}}</h3></div><div flex class=caixa-tabela-fieldset><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-numero-laudo-relatorio\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-tipo\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-data\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-imovel-avaliado\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-valor-imovel\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"laudoRelatorioAvaliacao in paginationDadosUtilizacao\"><td md-cell><a href=\"\" ng-click=redirecionarDetalharLaudoAvaliacao(laudoRelatorioAvaliacao.id) target=_blank>{{laudoRelatorioAvaliacao.numero}}</a></td><td md-cell>{{tiposIdentificacao[laudoRelatorioAvaliacao.identificacao.tipo]}}</td><td md-cell>{{laudoRelatorioAvaliacao.dataAssinatura | date: \'dd/MM/yyyy\' : \'-0300\'}}</td><td md-cell><a href=\"\" ng-click=ripImovelLink(laudoRelatorioAvaliacao.identificacao.rip) target=_blank>{{laudoRelatorioAvaliacao.identificacao.rip}}</a></td><td md-cell><label ng-if=\"validarValorLaudoRelatorio(laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor, laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao)\" ng-bind=\"validarValorLaudoRelatorio(laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor, laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao);\"></label><p ng-if=\"!validarValorLaudoRelatorio(laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor, laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao)\" class=text-center>-</p></td></tr></tbody></table></md-table-container></md-content></div></div></div><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina>{{\'label-localizacao\' | translate}}</h3></div></div><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=90 flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><label>{{\'label-cep\' | translate}}:</label><input ng-disabled=detalharAmostra ng-model=amostra.enderecamento.cep ui-br-cep-mask=\"\" ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-cep\' | translate}}\" required ng-blur=pesquisarEnderecoPorCep();></md-input-container><md-input-container flex=10 class=rip-link style=\"margin-top: -20px !important\"><a href=\"\" ng-click=redirecionarConsultarCep(); ng-show=!detalharAmostra>{{\'label-nao-sei-cep\'| translate}}</a></md-input-container></div><div layout=row layout-xs=column layout-sm=column ng-show=cepEncontrado><md-checkbox class=md-primary ng-model=mostrarUniao ng-change=apresentaImoveisUniao(mostrarUniao)><label class=text-black>Apresentar imóveis da união</label></md-checkbox></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><ci-mapa-avaliacao mostrar-amostras=true showminimapa=true heightminimapa=50 widthminimapa=50 imovel=imovel desabilitar=detalharAmostra bt-marcar=!detalharAmostra bt-camada=true bt-remover=!detalharAmostra amostras=amostras visualizar=true latitude=amostra.latitude longitude=amostra.longitude detalhar-amostra=detalharAmostra bloquear-mapa=bloquearMapa bt-selecionar=true></ci-mapa-avaliacao></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-uf\' | translate}}</label><md-select ng-model=amostra.enderecamento.uf ng-change=pesquisarMunicipiosPorUf() ng-required=isMatricula ng-disabled=\"detalharAmostra  || cepEncontrado\"><md-option ng-repeat=\"uf in listaUFs | orderBy: \'sigla\'\" ng-value=uf.sigla ng-selected=\"amostra.enderecamento.uf==uf.sigla\">{{uf.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-municipio\' | translate}}</label><md-select ng-model=amostra.enderecamento.municipio ng-selected=\"amostra.enderecamento.municipio == municipio.nome\" ng-disabled=\"detalharAmostra || !amostra.enderecamento.uf || cepEncontrado\"><md-option ng-repeat=\"municipio in listaMunicipios | orderBy: \'nome\'\" ng-value=municipio.nome>{{municipio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tipo-logradouro\' | translate}}</label><md-select ng-model=amostra.enderecamento.tipoLogradouro name=\"{{\'label-tipo-logradouro\' | translate}}\" placeholder=\"{{\'label-tipo-logradouro\' | translate}}\" ng-disabled=\"detalharAmostra || cepEncontrado\"><md-option ng-repeat=\"tipoLogradouro in listaTiposLogradouro\" ng-value=tipoLogradouro.descricao>{{tipoLogradouro.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-logradouro\' | translate}}</label><input ng-model=amostra.enderecamento.logradouro ng-disabled=\"detalharAmostra || cepEncontrado\"></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-numero\' | translate}}</label><input ng-model=amostra.enderecamento.numero ng-disabled=detalharAmostra></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-bairro-distrito\' | translate}}</label><input ng-model=amostra.enderecamento.bairro ng-disabled=\"detalharAmostra || cepEncontrado\"></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-complemento\' | translate}}</label><input ng-model=amostra.enderecamento.complemento ng-disabled=detalharAmostra></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaTelaAnterior()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button ng-if=!detalharAmostra class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarAmostra()><label>{{\'button-adicionar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/amostra/views/cancelarAmostra.html","<form name=formularioCancelarAmostra novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-cancelar-dados-mercado\' | translate}} {{\' - \' + amostra.codigoAmostra}}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-nome-responsavel\' | translate}}:</label><input type=text ng-disabled=true ng-model=amostra.responsavelCancelamento></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-data\' | translate}}:</label><input type=text ng-disabled=true ng-model=dataMostrar></md-input-container></div><div class=m-b-30 layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-offset=5><label>{{\'label-justificativa\' | translate}}</label><textarea rows=4 cols=50 ng-model=amostra.justificativa maxlength=500 required></textarea><span style=\"color: #cc0000; float: right\">{{0 + amostra.justificativa.length}} / 500</span></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaConsultarAmostra()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=cancelarAmostra(formularioCancelarAmostra);><label>{{\'button-confirmar-cancelamento\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/amostra/views/completarDadosAmostra.html","<form name=formularioDadosAmostra novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-completar-informacoes-dados-mercado\' | translate}}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=33><label>{{\'label-tipo\' | translate}}:</label><md-select ng-model=amostra.tipoAmostra ng-change=pesquisarTiposDadosComplementares(amostra.tipoAmostra) name=\"{{nomeCampo}} - Tipo Amostra\" required ng-disabled=\"detalharAmostra || (editarAmostra && !desbloquearAmostra)\"><md-option ng-repeat=\"elemento in listaTiposAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-area-terreno\' | translate}}</label><input type=text ng-model=amostra.areaTerreno name=\"{{nomeCampo}} - {{\'label-area-terreno\' | translate}}\" ui-number-mask=2 ng-blur=changeAreaTerreno(amostra.areaTerreno) ng-blur=changeValores(amostra.areaTerreno) ng-required=\"amostra.areaConstruida == null\" ng-disabled=detalharAmostra></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 ng-show=\"amostra.tipoAmostra != \'TERRENO\'\"><label>{{\'label-area-construida\' | translate}}</label><input type=text ng-model=amostra.areaConstruida name=\"{{nomeCampo}} - {{\'label-area-construida\' | translate}}\" ui-number-mask=2 ng-blur=changeAreaConstruida(amostra.areaConstruida) ng-required=\"amostra.areaTerreno == null\" ng-disabled=detalharAmostra></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33><label>{{\'label-classificacao\' | translate}}:</label><md-select ng-model=amostra.classificacaoAmostra ng-change=changeClassificacaoAmostra(amostra.classificacaoAmostra) name=\"{{nomeCampo}} - Classificação da Amostra\" ng-disabled=detalharAmostra><md-option ng-repeat=\"elemento in listaClassificacaoAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33><div layout=row><label ng-show=!amostra.classificacaoAmostra>{{\'label-valor\' | translate}}</label><label ng-show=amostra.classificacaoAmostra>{{(amostra.classificacaoAmostra == \'TRANSACAO\' ? \'label-valor-transacao\' : \'label-valor-oferta\') | translate}}</label></div><input type=text ng-blur=changeValorAmotra(amostra.valor) ng-model=amostra.valor name=\"{{nomeCampo}} - Valor\" ui-number-mask=2 ng-required=\"amostra.classificacaoAmostra != null\" ng-disabled=detalharAmostra></md-input-container><div flex=33><span ng-bind=valorPorExtenso></span></div></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33><label>{{\'label-classificacao-valor-locacao\' | translate}}:</label><md-select ng-model=amostra.classificacaoAmostraLocacao ng-change=changeClassificacaoAmostraLocacao(amostra.classificacaoAmostraLocacao) name=\"{{nomeCampo}} - Classificação da Amostra quanto ao valor de locação\" ng-disabled=detalharAmostra><md-option ng-repeat=\"elemento in listaClassificacaoAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33><div layout=row><label ng-show=!amostra.classificacaoAmostraLocacao>{{\'label-valor-locacao\' | translate}}</label><label ng-show=amostra.classificacaoAmostraLocacao>{{(amostra.classificacaoAmostraLocacao == \'OFERTA\' ? \'label-valor-oferta-locacao\' : \'label-valor-transacao-locacao\') | translate}}</label></div><input type=text ng-blur=changeValorAmotraLocacao(amostra.valorLocacao) ng-model=amostra.valorLocacao name=\"{{nomeCampo}} - Valor Locação\" ui-number-mask=2 ng-required=\"amostra.classificacaoAmostraLocacao != null\" ng-disabled=detalharAmostra></md-input-container><div flex=33><span ng-bind=valorLocacaoPorExtenso></span></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-informante-fonte\' | translate}}</label><textarea class=barraRolagemTextArea ng-model=amostra.informanteFonte ng-disabled=detalharAmostra ui-mask-placeholder-char=space required></textarea></md-input-container></div><div layout=row layout-xs=column class=m-b-10><md-checkbox class=md-primary ng-model=amostra.isDadosComplementares ng-change=limparDadosComplementares(amostra.isDadosComplementares); name=\"{{\'label-incluir-dados-complementares\' | translate}}\" ng-disabled=detalharAmostra><label class=text-black>{{\'label-incluir-dados-complementares\' | translate}}</label></md-checkbox></div><dados-complementares-amostra ng-if=amostra.isDadosComplementares dados-complementares=amostra.dadosComplementares detalhar-amostra=detalharAmostra gerar-planilha=false lista-tipos-dados-complementares=listaTiposDadosComplementares></dados-complementares-amostra><div layout=row layout-xs=column class=m-b-10><md-checkbox class=md-primary ng-model=amostra.isArquivosComplementares ng-change=limparArquivosComplementares(amostra.isArquivosComplementares); name=\"{{\'label-incluir-arquivos-complementares\' | translate}}\" ng-disabled=detalharAmostra><label class=text-black>{{\'label-incluir-arquivos-complementares\' | translate}}</label></md-checkbox></div><documento-laudo-avaliacao-padrao ng-if=amostra.isArquivosComplementares lista-documentos-laudo=amostra.arquivosComplementares campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_01\'\" detalhar-laudo-avaliacao=detalharAmostra titulo=\"\'label-arquivos-complementares\' | translate\"></documento-laudo-avaliacao-padrao><documento-fonte-amostra-padrao lista-documentos=amostra.listaDocumentoFonte detalhar-amostra=detalharAmostra titulo=\"\'Fonte\' | translate\"></documento-fonte-amostra-padrao><documento-laudo-avaliacao-padrao lista-documentos-laudo=amostra.fotos campo-data=\"\'true\'\" tipo-foto=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_01\'\" detalhar-laudo-avaliacao=detalharAmostra titulo=\"\'title-fotos\' | translate\"></documento-laudo-avaliacao-padrao><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina>{{\'label-localizacao\' | translate}}</h3></div></div><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=90 flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><label>{{\'label-cep\' | translate}}:</label><input ng-disabled=detalharAmostra ng-model=amostra.enderecamento.cep ui-br-cep-mask=\"\" ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-cep\' | translate}}\" required ng-blur=pesquisarEnderecoPorCep();></md-input-container><md-input-container flex=10 class=rip-link style=\"margin-top: -20px !important\"><a href=\"\" ng-click=redirecionarConsultarCep(); ng-show=!detalharAmostra>{{\'label-nao-sei-cep\'| translate}}</a></md-input-container></div><div layout=row layout-xs=column layout-sm=column ng-show=cepEncontrado><md-checkbox class=md-primary ng-model=mostrarUniao ng-change=apresentaImoveisUniao(mostrarUniao)><label class=text-black>Apresentar imóveis da união</label></md-checkbox></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><ci-mapa-avaliacao mostrar-amostras=true showminimapa=true heightminimapa=50 widthminimapa=50 imovel=imovel desabilitar=detalharAmostra bt-marcar=!detalharAmostra bt-camada=true bt-remover=!detalharAmostra amostras=amostras visualizar=true latitude=amostra.latitude longitude=amostra.longitude detalhar-amostra=detalharAmostra bloquear-mapa=bloquearMapa bt-selecionar=true></ci-mapa-avaliacao></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-uf\' | translate}}</label><md-select ng-model=amostra.enderecamento.uf ng-change=pesquisarMunicipiosPorUf() ng-required=isMatricula ng-disabled=\"detalharAmostra  || cepEncontrado\"><md-option ng-repeat=\"uf in listaUFs | orderBy: \'sigla\'\" ng-value=uf.sigla ng-selected=\"amostra.enderecamento.uf==uf.sigla\">{{uf.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-municipio\' | translate}}</label><md-select ng-model=amostra.enderecamento.municipio ng-selected=\"amostra.enderecamento.municipio == municipio.nome\" ng-disabled=\"detalharAmostra || !amostra.enderecamento.uf || cepEncontrado\"><md-option ng-repeat=\"municipio in listaMunicipios | orderBy: \'nome\'\" ng-value=municipio.nome>{{municipio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tipo-logradouro\' | translate}}</label><md-select ng-model=amostra.enderecamento.tipoLogradouro name=\"{{\'label-tipo-logradouro\' | translate}}\" placeholder=\"{{\'label-tipo-logradouro\' | translate}}\" ng-disabled=\"detalharAmostra || cepEncontrado\"><md-option ng-repeat=\"tipoLogradouro in listaTiposLogradouro\" ng-value=tipoLogradouro.descricao>{{tipoLogradouro.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-logradouro\' | translate}}</label><input ng-model=amostra.enderecamento.logradouro ng-disabled=\"detalharAmostra || cepEncontrado\"></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-numero\' | translate}}</label><input ng-model=amostra.enderecamento.numero ng-disabled=detalharAmostra></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-bairro-distrito\' | translate}}</label><input ng-model=amostra.enderecamento.bairro ng-disabled=\"detalharAmostra || cepEncontrado\"></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-complemento\' | translate}}</label><input ng-model=amostra.enderecamento.complemento ng-disabled=detalharAmostra></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaTelaAnterior()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button ng-if=!detalharAmostra class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarAmostra()><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/amostra/views/consultarAmostra.html","<form name=formularioConsultaAmostra><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-consultar-dados-mercado\' | translate}}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50><label>{{\'label-tipo\' | translate}}:</label><md-select ng-model=amostra.tipoAmostra name=\"{{nomeCampo}} - Tipo\"><md-option ng-repeat=\"elemento in listaTiposAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-codigo\' | translate}}</label><input type=text ng-model=amostra.codigoAmostra name=\"{{nomeCampo}} - {{\'label-codigo\' | translate}}\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50><label>{{\'label-classificacao\' | translate}}:</label><md-select ng-model=amostra.classificacaoAmostra name=\"{{nomeCampo}} - Classificação\"><md-option ng-repeat=\"elemento in listaClassificacaoAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50><label>{{\'label-situacao\' | translate}}:</label><md-select ng-model=amostra.situacaoAmostra name=\"{{nomeCampo}} - Situação\"><md-option ng-repeat=\"(k, v) in listaSituacaoAmostra\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div class=m-b-20 layout=row layout-xs=column><md-checkbox class=md-primary ng-model=amostra.isArea ng-change=changeSomenteArea() name=\"{{\'label-somente-dados-mercado-area-entre\' | translate}}\"><label class=text-black>{{\'label-somente-dados-mercado-area-entre\' | translate}}</label></md-checkbox></div><div layout=row layout-xs=column ng-show=amostra.isArea><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-area-terreno\' | translate}}:</span></span></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px;  margin-left: 10px\"><span class=datepicker-label><span>{{\'label-area-construida\' | translate}}:</span></span></div></div><div layout=row layout-xs=column flex-sm=column ng-show=amostra.isArea><label style=\"margin-top: 12px\">de</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.areaTerreno name=\"{{nomeCampo}} - {{\'label-area-terreno\' | translate}}\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">a</label><md-input-container class=md-block flex=25><input style=\"width: 99%\" type=text ng-model=amostra.areaTerrenoFim name=\"{{nomeCampo}} - {{\'label-area-terreno 2\' | translate}}\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">de</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.areaConstruida name=\"{{nomeCampo}} - {{\'label-area-construida\' | translate}}\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">a</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.areaConstruidaFim name=\"{{nomeCampo}} - {{\'label-area-construida 2\' | translate}}\" ui-number-mask=2></md-input-container></div><div class=m-b-20 layout=row layout-xs=column><md-checkbox class=md-primary ng-model=amostra.isValor ng-change=changeSomenteValor() name=\"{{\'label-somente-dados-mercado-valor-entre\' | translate}}\"><label class=text-black>{{\'label-somente-dados-mercado-valor-entre\' | translate}}</label></md-checkbox></div><div layout=row layout-xs=column ng-show=amostra.isValor><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-valor\' | translate}}:</span></span></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px; margin-left: 10px\"><span class=datepicker-label><span>{{\'label-valor-locacao\' | translate}}:</span></span></div></div><div layout=row layout-xs=column flex-sm=column ng-show=amostra.isValor><label style=\"margin-top: 12px\">de</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.valor name=\"{{nomeCampo}} - Valor\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">a</label><md-input-container class=md-block flex=25><input style=\"width: 99%\" type=text ng-model=amostra.valorFim name=\"{{nomeCampo}} - Valor 2\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">de</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.valorLocacao name=\"{{nomeCampo}} - Valor Locação\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">a</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.valorLocacaoFim name=\"{{nomeCampo}} - Valor Locação 2\" ui-number-mask=2></md-input-container></div><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><label>{{\'label-cep\' | translate}}:</label><input ng-model=amostra.enderecamento.cep ui-br-cep-mask=\"\" ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-cep\' | translate}}\" ng-blur=pesquisarEnderecoPorCep();></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-uf\' | translate}}</label><md-select ng-model=amostra.enderecamento.uf ng-change=pesquisarMunicipiosPorUf()><md-option ng-repeat=\"uf in listaUFs | orderBy: \'sigla\'\" ng-value=uf.sigla ng-selected=\"amostra.enderecamento.uf==uf.sigla\">{{uf.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column layout-sm=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-municipio\' | translate}}</label><md-select ng-model=amostra.enderecamento.municipio ng-selected=\"amostra.enderecamento.municipio == municipio.nome\" ng-disabled=!amostra.enderecamento.uf><md-option ng-repeat=\"municipio in listaMunicipios | orderBy: \'nome\'\" ng-value=municipio.nome>{{municipio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-bairro-distrito\' | translate}}</label><input ng-model=amostra.enderecamento.bairro></md-input-container></div><div layout=row layout-xs=column><div flex-xs=100 flex-sm=100 flex=33></div><div class=md-block flex-gt-xs flex=33 style=\"margin-top: -15px; margin-left: 20px\"><span class=datepicker-label><span>{{\'label-periodo-atualizacao\' | translate}}:</span></span></div></div><div layout=row layout-xs=column layout-sm=column><md-input-container class=md-block flex=33 flex-xs layout-xs=row layout-sm=row><label>{{\'label-dados-mercado-ja-utilizado\' | translate}}:</label><md-select ng-model=amostra.isAmostraUtilizada name=\"{{nomeCampo}} - {{\'label-dados-mercado-ja-utilizado\' | translate}}\" placeholder=\"{{\'label-dados-mercado-ja-utilizado\' | translate}}\"><md-option ng-repeat=\"item in condicoes\" ng-value=item.valor>{{item.nome}}</md-option></md-select></md-input-container><md-container flex=66 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row layout-xs=row><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=amostra.dtInicio md-max-date=hoje md-placeholder=\"{{\'placeholder-data-inicial\' | translate}}\"></md-datepicker></div><label style=\"margin-top: 12px;margin-left: 10px\">a</label><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=amostra.dtFim md-max-date=hoje md-placeholder=\"{{\'placeholder-data-final\' | translate}}\"></md-datepicker></div></div></md-container></div><div class=m-b-20 layout=row layout-xs=column><md-checkbox class=md-primary ng-model=amostra.isFotos name=\"{{\'label-somente-dados-mercado-fotos\' | translate}}\"><label class=text-black>{{\'label-somente-dados-mercado-fotos\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=amostra.isFontes name=\"{{\'label-somente-dados-mercado-fontes\' | translate}}\"><label class=text-black>{{\'label-somente-dados-mercado-fontes\' | translate}}</label></md-checkbox></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-apresentacao-consulta\' | translate}}:</span></span></div></div><div layout=row layout-xs=column flex=100 flex-sm=column flex-sm=100 flex-xs=100><md-input class=md-block flex=50 flex-xs=100 flex-sm=100 layout=column><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=amostra.apresentacao name=\"{{nomeCampo}} - Apresentacao\" ng-change=changeMapa(amostra.apresentacao)><div layout=row><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary\" ng-value=\"\'TABELA\'\"><div class=texto-preto>{{\'label-tabela\' | translate}}</div></md-radio-button><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary\" ng-value=\"\'MAPA\'\"><div class=texto-preto>{{\'label-mapa\' | translate}}</div></md-radio-button></div></md-radio-group></md-input><div flex=50></div></div><div class=\"md-block m-b-20\" flex-gt-xs flex=100><div layout=row layout-align=end><md-button ng-if=\"usuarioLogado.AVALIACAO_INCLUIR_EDITAR_DADO_MERCADO == \'true\'\" class=\"md-primary md-raised\" type=button ng-click=incluirAmostra()><label>{{\'button-novo\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=limparCamposPesquisa()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=pesquisarDadosAmostra(amostra.apresentacao)><label>{{\'button-pesquisar\' | translate}}</label></md-button></div></div><div layout=row layout-align=\"center start\" ng-show=\"amostra.apresentacao === \'TABELA\'\"><div flex><md-table-container><table md-table><thead md-head md-order=order md-on-reorder=pesquisarDadosAmostra><tr md-row><th md-column width=5%><div layout=row layout-align=\"center center\"><md-checkbox class=md-primary ng-change=selecionarTodosItens() ng-model=isTodasAmostraSelecionada></md-checkbox></div></th><th md-column width=5%>{{\'tr-foto\' | translate}}</th><th md-column width=10% md-order-by=codigoAmostra>{{\'tr-codigo\' | translate}}</th><th md-column width=10%>{{\'tr-tipo\' | translate}}</th><th md-column width=20%>{{\'tr-informante-fonte\' | translate}}</th><th md-column width=30%>{{\'tr-endereco\' | translate}}</th><th md-column width=10% md-order-by=dtAtualizacao>{{\'tr-data-atualizacao\' | translate}}</th><th md-column width=10%>{{\'tr-situacao\' | translate}}</th><th md-column width=5%>{{\'tr-acao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaDadosAmostra\"><td class=th-margin-pvgs md-cell><div layout=row layout-align=\"center center\"><md-checkbox class=md-primary ng-change=selecionarItem() ng-model=item.isAmostraSelecionada></md-checkbox></div></td><td md-cell><div layout=column style=\"width: auto\"><div width=100px height=100px><a ng-click=\"showExibirImagensImovel($event, item.fotos)\"><img ng-src=\"{{item.fotos.length > 0 ? gerarImagem(item.fotos[0].arquivo.binario) : \'http://i.imgur.com/YV0WS3k.png\'}}\" style=\"max-height: 100px; max-width: 100px\"></a></div></div></td><td md-cell><span ng-bind=item.codigoAmostra></span></td><td md-cell><span ng-bind=listaTiposAmostraTab[item.tipoAmostra]></span></td><td md-cell><span ng-bind=item.informanteFonte></span></td><td md-cell><span ng-show=item.enderecamento.numero ng-bind=\"item.enderecamento.logradouro + \', Número \'\n                                                        + item.enderecamento.numero + \', \'\n                                                        + item.enderecamento.bairro + \', \'\n                                                        + item.enderecamento.municipio + \'/\'\n                                                        + item.enderecamento.uf + \' - \'\n                                                        + (item.enderecamento.cep | cep)\"></span> <span ng-show=!item.enderecamento.numero ng-bind=\"item.enderecamento.logradouro + \', \'\n                                                        + item.enderecamento.bairro + \', \'\n                                                        + item.enderecamento.municipio + \'/\'\n                                                        + item.enderecamento.uf + \' - \'\n                                                        + (item.enderecamento.cep | cep)\"></span></td><td md-cell><span>{{item.dtAtualizacao | date: \'dd/MM/yyyy\' : \'-0300\'}}</span></td><td md-cell><span ng-bind=listaSituacaoAmostra[item.situacaoAmostra]></span></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-if=\"usuarioLogado.AVALIACAO_INCLUIR_EDITAR_DADO_MERCADO == \'true\'\" class=\"md-fab md-raised md-mini\" ng-click=editarAmostra(item.id) ng-show=\"item.situacaoAmostra!=\'CANCELADO\'\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=detalharAmostra(item.id)><md-icon ng-bind=\"\'search\'\"><md-tooltip>{{\'label-detalhar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"usuarioLogado.AVALIACAO_DADO_MERCADO_EXCLUIR == \'true\'\" class=\"md-fab md-raised md-mini\" ng-click=cancelarAmostra(item.id); ng-show=\"item.situacaoAmostra!=\'CANCELADO\'\"><md-icon ng-bind=\"\'clear\'\"><md-tooltip>{{\'label-cancelar\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"abrirDialogMapaAmostra($event, item)\"><md-icon ng-bind=\"\'map\'\"><md-tooltip>{{\'label-ver-localizacao\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarDadosAmostra md-page-select></md-table-pagination></div></div><div class=\"md-block m-b-20\" flex-gt-xs flex=100><div layout=row layout-align=end ng-show=\"listaDadosAmostrasSelecionadas.length > 0\"><md-button class=\"md-primary md-raised\" type=button ng-click=gerarPlanilhaAmostras()><label>{{\'button-exportar-planilha-dados-mercado\' | translate}}</label></md-button></div></div><div layout=row layout-xs=column flex-sm=column ng-if=carregarMapa><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><ci-mapa-avaliacao mostrar-amostras=true showminimapa=true heightminimapa=50 widthminimapa=50 imovel=imovel bt-marcar=false bt-camada=true bt-remover=false visualizar=true detalhar-amostra=true consultar-adicionar-amostras=true editar-amostra=editarAmostra(idAmostra) bloquear-mapa=false visualizar-amostra=detalharAmostra(idAmostra) cancelar-amostra=cancelarAmostra(idAmostra)></ci-mapa-avaliacao></md-input-container></div></form>");
$templateCache.put("scripts-avaliacao/pages/amostra/views/consultarUploadAmostra.html","<form name=formularioDadosAmostra novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-consultar-upload-dados-mercado\' | translate}}</h2></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px; margin-left: 20px\"><span class=datepicker-label><span>{{\'label-data-upload\' | translate}}:</span></span></div></div><div layout=row layout-xs=column layout-sm=column><md-container flex=50 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row layout-xs=row><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=filtro.dtInicio md-max-date=hoje md-placeholder=\"{{\'placeholder-data-inicial\' | translate}}\"></md-datepicker></div><label style=\"margin-top: 12px;margin-left: 10px\">a</label><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=filtro.dtFim md-max-date=hoje md-placeholder=\"{{\'placeholder-data-final\' | translate}}\"></md-datepicker></div></div></md-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-responsavel\' | translate}}</label><input type=text ng-model=filtro.responsavel name=\"{{nomeCampo}} - {{\'label-codigo\' | translate}}\"></md-input-container></div><div class=\"md-block m-b-20\" flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=limparFiltro()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=pesquisarDadosUploadAmostra()><label>{{\'button-pesquisar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=redirecionarUploadPlanilhaAmostra()><label>{{\'button-novo\' | translate}}</label></md-button></div></div><div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column width=15%>{{\'label-data-upload\' | translate}}</th><th md-column width=15%>{{\'label-dados-completos\' | translate}}</th><th md-column width=15%>{{\'label-dados-pendentes\' | translate}}</th><th md-column width=50%>{{\'label-responsavel\' | translate}}</th><th md-column width=5%>{{\'tr-acao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in dadosUploadAmostra\"><td md-cell><span>{{item.dtUpload | date: \'dd/MM/yyyy\' : \'-0300\'}}</span></td><td md-cell><span ng-bind=item.totalCompletados></span></td><td md-cell><span ng-bind=item.totalPendentes></span></td><td md-cell><span ng-bind=(item.responsavel)></span></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=editarDadoUploadAmostra(item)><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=excluirDadoUploadAmostra(item.id)><md-icon ng-bind=\"\'delete\'\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarDadosUploadAmostra md-page-select></md-table-pagination></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/amostra/views/gerarPlanilhaAmostra.html","<form name=formularioDadosAmostra novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-gerar-planilha-dados-mercado\' | translate}}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100><label>{{\'label-tipo\' | translate}}:</label><md-select ng-model=amostra.tipoAmostraTemp ng-change=changeTipoAmostra(); name=\"{{nomeCampo}} - Tipo Amostra\" required ng-disabled=\"detalharAmostra || (editarAmostra && !desbloquearAmostra)\"><md-option ng-repeat=\"elemento in listaTiposAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column class=m-b-10><md-checkbox class=md-primary ng-model=amostra.isDadosComplementares ng-change=limparDadosComplementares(amostra.isDadosComplementares); name=\"{{\'label-incluir-dados-complementares\' | translate}}\" ng-disabled=detalharAmostra><label class=text-black>{{\'label-incluir-dados-complementares\' | translate}}</label></md-checkbox></div><dados-complementares-amostra ng-if=amostra.isDadosComplementares dados-complementares=$parent.amostra.dadosComplementares detalhar-amostra=detalharAmostra gerar-planilha=true lista-tipos-dados-complementares=listaTiposDadosComplementares></dados-complementares-amostra><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><a style=\"text-decoration: underline; color: blue; cursor: pointer\" class=m-t-15 ng-href=\"\" target=_blank ng-click=showDialogOrientacoesPreenchimentoArquivo($event)>{{\'label-orientacoes-preenchimento-arquivo\' | translate}}</a><md-button ng-if=!detalharAmostra class=\"md-primary md-raised m-l-15\" type=button ng-click=gerarPlanilha()><label>{{\'button-gerar-planilha-dados-mercado\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/amostra/views/inserirAmostra.html","<form name=formularioDadosAmostra novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina ng-show=\"!detalharAmostra && !editarAmostra\">{{\'title-inserir-dado-mercado\' | translate}}</h2><h2 class=titulo-pagina ng-show=detalharAmostra>{{\'title-detalhar-dado-mercado\' | translate}} {{\' - \' + amostra.codigoAmostra}}</h2><h2 class=titulo-pagina ng-show=editarAmostra>{{\'title-editar-dado-mercado\' | translate}} {{\' - \' + amostra.codigoAmostra}}</h2></div></div><fieldset style=\"margin-top: -20px\" class=fieldsetArredondado ng-show=\"detalharAmostra && amostra.dtCancelamento\"><div layout=row flex layout-align=\"start start\"><h3 class=subtitulo-pagina>{{\'title-dados-cancelamento\' | translate}}</h3></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-data\' | translate}}:</label><input type=text ng-model=amostra.dtCancelamento ng-disabled=detalharAmostra></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-responsavel\' | translate}}:</label><input type=text ng-model=amostra.responsavelCancelamento ng-disabled=detalharAmostra></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-justificativa\' | translate}}</label><textarea rows=4 cols=50 ng-model=amostra.justificativa ng-disabled=detalharAmostra></textarea></md-input-container></div></fieldset><div layout=row layout-xs=column><md-input-container class=md-block flex=33><label>{{\'label-tipo\' | translate}}:</label><md-select ng-model=amostra.tipoAmostraTemp ng-change=changeTipoAmostra(); name=\"{{nomeCampo}} - Tipo Amostra\" required ng-disabled=\"detalharAmostra || (editarAmostra && !desbloquearAmostra)\"><md-option ng-repeat=\"elemento in listaTiposAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-area-terreno\' | translate}}</label><input type=text ng-model=amostra.areaTerreno name=\"{{nomeCampo}} - {{\'label-area-terreno\' | translate}}\" ui-number-mask=2 ng-blur=changeAreaTerreno(amostra.areaTerreno) ng-blur=changeValores(amostra.areaTerreno) ng-required=\"amostra.areaConstruida == null\" ng-disabled=detalharAmostra></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 ng-show=\"amostra.tipoAmostra != \'TERRENO\'\"><label>{{\'label-area-construida\' | translate}}</label><input type=text ng-model=amostra.areaConstruida name=\"{{nomeCampo}} - {{\'label-area-construida\' | translate}}\" ui-number-mask=2 ng-blur=changeAreaConstruida(amostra.areaConstruida) ng-required=\"amostra.areaTerreno == null\" ng-disabled=detalharAmostra></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33><label>{{\'label-classificacao\' | translate}}:</label><md-select ng-model=amostra.classificacaoAmostra ng-change=changeClassificacaoAmostra(amostra.classificacaoAmostra) ng-required=\"amostra.classificacaoAmostraLocacao == null\" name=\"{{nomeCampo}} - Classificação da Amostra\" ng-disabled=detalharAmostra><md-option ng-repeat=\"elemento in listaClassificacaoAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33><div layout=row><label ng-show=!amostra.classificacaoAmostra>{{\'label-valor\' | translate}}</label><label ng-show=amostra.classificacaoAmostra>{{(amostra.classificacaoAmostra == \'TRANSACAO\' ? \'label-valor-transacao\' : \'label-valor-oferta\') | translate}}</label></div><input type=text ng-blur=changeValorAmotra(amostra.valor) ng-model=amostra.valor name=\"{{nomeCampo}} - Valor Amostra\" ui-number-mask=2 ng-required=\"amostra.classificacaoAmostra != null\" ng-disabled=detalharAmostra></md-input-container><div flex=33><span ng-bind=valorPorExtenso></span></div></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33><label>{{\'label-classificacao-valor-locacao\' | translate}}:</label><md-select ng-model=amostra.classificacaoAmostraLocacao ng-change=changeClassificacaoAmostraLocacao(amostra.classificacaoAmostraLocacao) ng-required=\"amostra.classificacaoAmostra == null\" name=\"{{nomeCampo}} - Classificação quanto ao valor de locação\" ng-disabled=detalharAmostra><md-option ng-repeat=\"elemento in listaClassificacaoAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33><div layout=row><label ng-show=!amostra.classificacaoAmostraLocacao>{{\'label-valor-locacao\' | translate}}</label><label ng-show=amostra.classificacaoAmostraLocacao>{{(amostra.classificacaoAmostraLocacao == \'OFERTA\' ? \'label-valor-oferta-locacao\' : \'label-valor-transacao-locacao\') | translate}}</label></div><input type=text ng-blur=changeValorAmotraLocacao(amostra.valorLocacao) ng-model=amostra.valorLocacao name=\"{{nomeCampo}} - Valor Amostra Locação\" ui-number-mask=2 ng-required=\"amostra.classificacaoAmostraLocacao != null\" ng-disabled=detalharAmostra></md-input-container><div flex=33><span ng-bind=valorLocacaoPorExtenso></span></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-informante-fonte\' | translate}}</label><textarea class=barraRolagemTextArea ng-model=amostra.informanteFonte ng-disabled=detalharAmostra ui-mask-placeholder-char=space required></textarea></md-input-container></div><div layout=row layout-xs=column class=m-b-10><md-checkbox class=md-primary ng-model=amostra.isDadosComplementares ng-change=limparDadosComplementares(amostra.isDadosComplementares); name=\"{{\'label-incluir-dados-complementares\' | translate}}\" ng-disabled=detalharAmostra><label class=text-black>{{\'label-incluir-dados-complementares\' | translate}}</label></md-checkbox></div><dados-complementares-amostra ng-if=amostra.isDadosComplementares dados-complementares=$parent.amostra.dadosComplementares detalhar-amostra=detalharAmostra gerar-planilha=false lista-tipos-dados-complementares=listaTiposDadosComplementares></dados-complementares-amostra><div layout=row layout-xs=column class=m-b-10><md-checkbox class=md-primary ng-model=amostra.isArquivosComplementares ng-change=limparArquivosComplementares(amostra.isArquivosComplementares); name=\"{{\'label-incluir-arquivos-complementares\' | translate}}\" ng-disabled=detalharAmostra><label class=text-black>{{\'label-incluir-arquivos-complementares\' | translate}}</label></md-checkbox></div><documento-laudo-avaliacao-padrao ng-if=amostra.isArquivosComplementares lista-documentos-laudo=amostra.arquivosComplementares campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_01\'\" detalhar-laudo-avaliacao=detalharAmostra titulo=\"\'label-arquivos-complementares\' | translate\"></documento-laudo-avaliacao-padrao><documento-fonte-amostra-padrao lista-documentos=amostra.listaDocumentoFonte detalhar-amostra=detalharAmostra titulo=\"\'Fonte\' | translate\"></documento-fonte-amostra-padrao><div layout=row layout-xs=column class=m-b-10><md-checkbox class=md-primary ng-model=amostra.isFotos ng-change=limparFotos(amostra.isFotos); name=\"{{\'label-incluir-fotos\' | translate}}\" ng-disabled=detalharAmostra><label class=text-black>{{\'label-incluir-fotos\' | translate}}</label></md-checkbox></div><documento-laudo-avaliacao-padrao ng-if=amostra.isFotos lista-documentos-laudo=amostra.fotos campo-data=\"\'true\'\" tipo-foto=\"\'true\'\" detalhar-laudo-avaliacao=detalharAmostra titulo=\"\'title-fotos\' | translate\"></documento-laudo-avaliacao-padrao><div class=upload-arquivos ng-if=\"(detalharAmostra || editarAmostra) && paginationDadosUtilizacao.length > 0\"><div layout=row flex layout-align=\"start start\"><h3 class=subtitulo-pagina>{{\'title-utilizacao\' | translate}}</h3></div><div flex class=caixa-tabela-fieldset><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-numero-laudo-relatorio\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-tipo\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-data\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-imovel-avaliado\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-valor-imovel\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"laudoRelatorioAvaliacao in paginationDadosUtilizacao\"><td md-cell><a href=\"\" ng-click=redirecionarDetalharLaudoAvaliacao(laudoRelatorioAvaliacao.id) target=_blank>{{laudoRelatorioAvaliacao.numero}}</a></td><td md-cell>{{tiposIdentificacao[laudoRelatorioAvaliacao.identificacao.tipo]}}</td><td md-cell>{{laudoRelatorioAvaliacao.dataAssinatura | date: \'dd/MM/yyyy\' : \'-0300\'}}</td><td md-cell><a href=\"\" ng-click=ripImovelLink(laudoRelatorioAvaliacao.identificacao.rip) target=_blank>{{laudoRelatorioAvaliacao.identificacao.rip}}</a></td><td md-cell><label ng-if=\"validarValorLaudoRelatorio(laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor, laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao)\" ng-bind=\"validarValorLaudoRelatorio(laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor, laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao);\"></label><p ng-if=\"!validarValorLaudoRelatorio(laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao.tipoValor, laudoRelatorioAvaliacao.avaliacao.resultadosAvaliacao)\" class=text-center>-</p></td></tr></tbody></table></md-table-container></md-content></div></div></div><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina>{{\'label-localizacao\' | translate}}</h3></div></div><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=90 flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><label>{{\'label-cep\' | translate}}:</label><input ng-disabled=detalharAmostra ng-model=amostra.enderecamento.cep ui-br-cep-mask=\"\" ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-cep\' | translate}}\" required ng-blur=pesquisarEnderecoPorCep();></md-input-container><md-input-container flex=10 class=rip-link style=\"margin-top: -20px !important\"><a href=\"\" ng-click=redirecionarConsultarCep(); ng-show=!detalharAmostra>{{\'label-nao-sei-cep\'| translate}}</a></md-input-container></div><div layout=row layout-xs=column layout-sm=column ng-show=cepEncontrado><md-checkbox class=md-primary ng-model=mostrarUniao ng-change=apresentaImoveisUniao(mostrarUniao)><label class=text-black>Apresentar imóveis da união</label></md-checkbox></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><ci-mapa-avaliacao mostrar-amostras=true showminimapa=true heightminimapa=100 widthminimapa=100 bt-marcar=!detalharAmostra bt-camada=true bt-remover=!detalharAmostra amostras=amostras visualizar=true latitude=amostra.latitude longitude=amostra.longitude detalhar-amostra=detalharAmostra bloquear-mapa=bloquearMapa bt-selecionar=!detalharAmostra></ci-mapa-avaliacao></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=\"md-block m-t-15\" flex=33 flex-xs=100 flex-sm=100><label>{{\'label-uf\' | translate}}</label><md-select ng-model=amostra.enderecamento.uf ng-change=pesquisarMunicipiosPorUf() ng-required=isMatricula ng-disabled=\"detalharAmostra  || cepEncontrado\"><md-option ng-repeat=\"uf in listaUFs | orderBy: \'sigla\'\" ng-value=uf.sigla ng-selected=\"amostra.enderecamento.uf==uf.sigla\">{{uf.descricao}}</md-option></md-select></md-input-container><md-input-container class=\"md-block m-t-15\" flex=33 flex-xs=100 flex-sm=100><label>{{\'label-municipio\' | translate}}</label><md-select ng-model=amostra.enderecamento.municipio ng-selected=\"amostra.enderecamento.municipio == municipio.nome\" ng-disabled=\"detalharAmostra || !amostra.enderecamento.uf || cepEncontrado\"><md-option ng-repeat=\"municipio in listaMunicipios | orderBy: \'nome\'\" ng-value=municipio.nome>{{municipio.nome}}</md-option></md-select></md-input-container><md-input-container class=\"md-block m-t-15\" flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tipo-logradouro\' | translate}}</label><md-select ng-model=amostra.enderecamento.tipoLogradouro name=\"{{\'label-tipo-logradouro\' | translate}}\" placeholder=\"{{\'label-tipo-logradouro\' | translate}}\" ng-disabled=\"detalharAmostra || (cepEncontrado && isCepGenerico)\"><md-option ng-repeat=\"tipoLogradouro in listaTiposLogradouro\" ng-value=tipoLogradouro.descricao>{{tipoLogradouro.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-logradouro\' | translate}}</label><input ng-model=amostra.enderecamento.logradouro ng-disabled=\"detalharAmostra || (cepEncontrado && isCepGenerico)\"></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-numero\' | translate}}</label><input ng-model=amostra.enderecamento.numero ng-disabled=detalharAmostra></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-bairro-distrito\' | translate}}</label><input ng-model=amostra.enderecamento.bairro ng-disabled=\"detalharAmostra || (cepEncontrado && isCepGenerico)\"></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-complemento\' | translate}}</label><input ng-model=amostra.enderecamento.complemento ng-disabled=detalharAmostra></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaConsultarAmostra()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button ng-if=\"!detalharAmostra && !editarAmostra\" class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarAmostra()><label>{{\'button-gravar\' | translate}}</label></md-button><md-button ng-if=editarAmostra class=\"md-primary md-raised m-l-15\" type=button ng-click=alterarAmostra()><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/amostra/views/selecionarAmostra.html","<form name=formularioConsultaAmostra><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-selecionar-dados-mercado\' | translate}}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50><label>{{\'label-tipo\' | translate}}:</label><md-select ng-model=amostra.tipoAmostra name=\"{{nomeCampo}} - Tipo\"><md-option ng-repeat=\"elemento in listaTiposAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-codigo\' | translate}}</label><input type=text ng-model=amostra.codigoAmostra name=\"{{nomeCampo}} - {{\'label-codigo\' | translate}}\"></md-input-container><md-input-container class=md-block flex=50><label>{{\'label-classificacao\' | translate}}:</label><md-select ng-model=amostra.classificacaoAmostra name=\"{{nomeCampo}} - Classificação da Amostra\"><md-option ng-repeat=\"elemento in listaClassificacaoAmostra | orderBy: \'chave\'\" ng-value=elemento.chave>{{elemento.valor}}</md-option></md-select></md-input-container></div><div class=m-b-20 layout=row layout-xs=column><md-checkbox class=md-primary ng-model=amostra.isArea ng-change=changeSomenteArea() name=\"{{\'label-somente-dados-mercado-area-entre\' | translate}}\"><label class=text-black>{{\'label-somente-dados-mercado-area-entre\' | translate}}</label></md-checkbox></div><div layout=row layout-xs=column ng-show=amostra.isArea><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-area-terreno\' | translate}}:</span></span></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px; margin-left: 10px\"><span class=datepicker-label><span>{{\'label-area-construida\' | translate}}:</span></span></div></div><div layout=row layout-xs=column flex-sm=column ng-show=amostra.isArea><label style=\"margin-top: 12px\">de</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.areaTerreno name=\"{{nomeCampo}} - {{\'label-area-terreno\' | translate}}\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">a</label><md-input-container class=md-block flex=25><input style=\"width: 99%\" type=text ng-model=amostra.areaTerrenoFim name=\"{{nomeCampo}} - {{\'label-area-terreno 2\' | translate}}\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">de</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.areaConstruida name=\"{{nomeCampo}} - {{\'label-area-construida\' | translate}}\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">a</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.areaConstruidaFim name=\"{{nomeCampo}} - {{\'label-area-construida 2\' | translate}}\" ui-number-mask=2></md-input-container></div><div class=m-b-20 layout=row layout-xs=column><md-checkbox class=md-primary ng-model=amostra.isValor ng-change=changeSomenteValor() name=\"{{\'label-somente-dados-mercado-valor-entre\' | translate}}\"><label class=text-black>{{\'label-somente-dados-mercado-valor-entre\' | translate}}</label></md-checkbox></div><div layout=row layout-xs=column ng-show=amostra.isValor><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-valor\' | translate}}:</span></span></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px;  margin-left: 10px\"><span class=datepicker-label><span>{{\'label-valor-locacao\' | translate}}:</span></span></div></div><div layout=row layout-xs=column flex-sm=column ng-show=amostra.isValor><label style=\"margin-top: 12px\">de</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.valor name=\"{{nomeCampo}} - Valor\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">a</label><md-input-container class=md-block flex=25><input style=\"width: 99%\" type=text ng-model=amostra.valorFim name=\"{{nomeCampo}} - Valor 2\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">de</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.valorLocacao name=\"{{nomeCampo}} - Valor Locação\" ui-number-mask=2></md-input-container><label style=\"margin-top: 12px\">a</label><md-input-container class=md-block flex=25><input type=text ng-model=amostra.valorLocacaoFim name=\"{{nomeCampo}} - Valor Locação 2\" ui-number-mask=2></md-input-container></div><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><label>{{\'label-cep\' | translate}}:</label><input ng-model=amostra.enderecamento.cep ui-br-cep-mask=\"\" ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-cep\' | translate}}\" ng-blur=pesquisarEnderecoPorCep();></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-uf\' | translate}}</label><md-select ng-model=amostra.enderecamento.uf ng-change=pesquisarMunicipiosPorUf()><md-option ng-repeat=\"uf in listaUFs | orderBy: \'sigla\'\" ng-value=uf.sigla ng-selected=\"amostra.enderecamento.uf==uf.sigla\">{{uf.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column layout-sm=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-municipio\' | translate}}</label><md-select ng-model=amostra.enderecamento.municipio ng-selected=\"amostra.enderecamento.municipio == municipio.nome\" ng-disabled=!amostra.enderecamento.uf><md-option ng-repeat=\"municipio in listaMunicipios | orderBy: \'nome\'\" ng-value=municipio.nome>{{municipio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-bairro-distrito\' | translate}}</label><input ng-model=amostra.enderecamento.bairro></md-input-container></div><div layout=row layout-xs=column><div flex-xs=100 flex-sm=100 flex=33></div><div class=md-block flex-gt-xs flex=33 style=\"margin-top: -15px; margin-left: 20px\"><span class=datepicker-label><span>{{\'label-periodo-atualizacao\' | translate}}:</span></span></div></div><div layout=row layout-xs=column layout-sm=column><md-input-container class=md-block flex=33 flex-xs layout-xs=row layout-sm=row><label>{{\'label-dados-mercado-ja-utilizado\' | translate}}:</label><md-select ng-model=amostra.isAmostraUtilizada name=\"{{nomeCampo}} - {{\'label-dados-mercado-ja-utilizado\' | translate}}\" placeholder=\"{{\'label-dados-mercado-ja-utilizado\' | translate}}\"><md-option ng-repeat=\"item in condicoes\" ng-value=item.valor>{{item.nome}}</md-option></md-select></md-input-container><md-container flex=66 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row layout-xs=row><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=amostra.dtInicio md-max-date=hoje md-placeholder=\"{{\'placeholder-data-inicial\' | translate}}\"></md-datepicker></div><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=amostra.dtFim md-max-date=hoje md-placeholder=\"{{\'placeholder-data-final\' | translate}}\"></md-datepicker></div></div></md-container></div><div class=m-b-20 layout=row layout-xs=column><md-checkbox class=md-primary ng-model=amostra.isFotos name=\"{{\'label-somente-dados-mercado-fotos\' | translate}}\"><label class=text-black>{{\'label-somente-dados-mercado-fotos\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=amostra.isFontes name=\"{{\'label-somente-dados-mercado-fontes\' | translate}}\"><label class=text-black>{{\'label-somente-dados-mercado-fontes\' | translate}}</label></md-checkbox></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-apresentacao-consulta\' | translate}}:</span></span></div></div><div layout=row layout-xs=column flex=100 flex-sm=column flex-sm=100 flex-xs=100><md-input class=md-block flex=50 flex-xs=100 flex-sm=100 layout=column><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=amostra.apresentacao name=\"{{nomeCampo}} - Apresentacao\" ng-change=changeMapa(amostra.apresentacao)><div layout=row><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary\" ng-value=\"\'TABELA\'\"><div class=texto-preto>{{\'label-tabela\' | translate}}</div></md-radio-button><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary\" ng-value=\"\'MAPA\'\"><div class=texto-preto>{{\'label-mapa\' | translate}}</div></md-radio-button></div></md-radio-group></md-input><div flex=50></div></div><div class=\"md-block m-b-20\" flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarTelaConsulta()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=limparCamposPesquisa()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=pesquisarDadosAmostra()><label>{{\'button-pesquisar\' | translate}}</label></md-button></div></div><div layout=row layout-align=\"center start\" ng-show=\"amostra.apresentacao === \'TABELA\'\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column width=5%><div layout=row layout-align=\"center center\"><md-checkbox class=md-primary ng-change=selecionarTodosItens() ng-model=isTodasAmostraSelecionada></md-checkbox></div></th><th md-column width=10%>{{\'tr-foto\' | translate}}</th><th md-column width=10%>{{\'tr-codigo\' | translate}}</th><th md-column width=10%>{{\'tr-tipo\' | translate}}</th><th md-column width=20%>{{\'tr-informante-fonte\' | translate}}</th><th md-column width=30%>{{\'tr-endereco\' | translate}}</th><th md-column width=10%>{{\'tr-data-atualizacao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaDadosAmostra\"><td class=th-margin-pvgs md-cell><div layout=row layout-align=\"center center\"><md-checkbox class=md-primary ng-change=selecionarItem() ng-model=item.isAmostraSelecionada></md-checkbox></div></td><td md-cell><div layout=column style=\"width: auto\"><div width=100px height=100px><a ng-click=\"showExibirImagensImovel($event, item.fotos)\"><img ng-src=\"{{item.fotos.length > 0 ? gerarImagem(item.fotos[0].arquivo.binario) : \'http://i.imgur.com/YV0WS3k.png\'}}\" style=\"max-height: 100px; max-width: 100px\"></a></div></div></td><td md-cell><span ng-bind=item.codigoAmostra></span></td><td md-cell><span ng-bind=listaTiposAmostraTab[item.tipoAmostra]></span></td><td md-cell><span ng-bind=item.informanteFonte></span></td><td md-cell><span ng-bind=\"item.enderecamento.logradouro + \', Número \'\n                                                        + item.enderecamento.numero + \', \'\n                                                        + item.enderecamento.bairro + \', \'\n                                                        + item.enderecamento.municipio + \'/\'\n                                                        + item.enderecamento.uf + \' - \'\n                                                        + (item.enderecamento.cep | cep)\"></span></td><td md-cell><span>{{item.dtAtualizacao | date: \'dd/MM/yyyy\' : \'-0300\'}}</span></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarDadosAmostra md-page-select></md-table-pagination></div></div><div layout=column flex=100 ng-if=carregarMapa><div layout=row><div layout=column flex=100 class=block-danger><div class=title><md-icon md-font-set=material-icons class=step style=\"font-size: 50px; height: 50px; width: 50px; display: inline-block; margin-top: -25px\">error_outline</md-icon><span style=\"display: inline-block\">Selecionar amostras para utilização<br>Para selecionar ou deselecionar uma amostra para utilização, clique sobre o ponto de localização da mesma no mapa abaixo.</span></div></div></div></div><br><div layout=row layout-xs=column flex-sm=column ng-if=carregarMapa><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><ci-mapa-avaliacao mostrar-amostras=true showminimapa=true heightminimapa=50 widthminimapa=50 bt-marcar=false bt-camada=true bt-remover=false amostras=amostras visualizar=true bloquear-mapa=false latitude=amostra.latitude longitude=amostra.longitude detalhar-amostra=false lista-amostras=listaDadosAmostrasSelecionadas mudar-cor=true bt-adicionar-amostras=true></ci-mapa-avaliacao></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=confimarAmostrasSelecionado();><label>{{\'button-confirmar-selecao-dados-mercado\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/amostra/views/uploadPlanilhaAmostra.html","<form name=formularioDadosAmostra novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-upload-planilha-dados-mercado\' | translate}}</h2></div></div><div class=m-b-10 flex-gt-xs flex=33 style=\"margin-top: -20px\"><md-button class=\"md-primary md-raised\" type=button ng-disabled=\"\" ngf-multiple=false ngf-select=adicionarArquivoUpload($files)><label>{{\'button-selecionar-arquivo\' | translate}}</label></md-button><span>{{file.name}}</span></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button ng-show=\"listaDadosAmostra.length == 0\" class=\"md-primary md-raised\" type=button ng-click=redirecionarParaConsultarUploadAmostra()><label>{{\'button-retornar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=limparArquivoUpload()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=uploadPlanilhaAmostra()><label>{{\'button-confirmar\' | translate}}</label></md-button></div></div></div><div layout=row flex=100 class=aviso-erro-importacao ng-show=showAvisoErroImportacao><div flex=10 layout=column style=\"margin: 5px; max-width: 80px !important\"><md-icon class=icon-aviso-erro-importacao ng-bind=\"\'info_outline\'\"></md-icon></div><div flex=90 layout=column><p style=\"color: #F44336\">{{\'mensagem-erro-importacao\' | translate}} <a href=\"\" ng-click=downloadRetorioImportacao()>relatorio_importacao.txt</a><br>{{\'mensagem-erro-importacao-cont\' | translate}} <a style=\"text-decoration: underline; color: blue; cursor: pointer\" class=m-t-15 ng-href=\"\" target=_blank ng-click=showDialogOrientacoesPreenchimentoArquivo($event)>{{\'label-orientacoes\' | translate}}</a></p></div></div><div ng-show=\"listaDadosAmostra.length > 0\"><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'label-responsavel\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{dadoUploadAmostra.responsavel}}</label></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'label-total-registros-importados\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{totalItems}}</label></div></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column width=5%><div layout=row layout-align=\"center center\"><md-checkbox class=md-primary ng-change=selecionarTodosItens() ng-model=isTodasAmostraSelecionada></md-checkbox></div></th><th md-column width=10%>{{\'tr-foto\' | translate}}</th><th md-column width=10%>{{\'tr-tipo\' | translate}}</th><th md-column width=30%>{{\'tr-informante-fonte\' | translate}}</th><th md-column width=40%>{{\'tr-endereco\' | translate}}</th><th md-column width=5%>{{\'tr-acao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaDadosAmostra\" ng-class=\"(item.situacaoAmostra == \'IMPORTACAO\' || item.situacaoAmostra == \'PENDENTE\') ? \'color-pendencia-preenchimento\' : \'\'\"><td class=th-margin-pvgs md-cell><div layout=row layout-align=\"center center\"><md-checkbox class=md-primary ng-change=selecionarItem() ng-model=item.isAmostraSelecionada></md-checkbox></div></td><td md-cell><div layout=column style=\"width: auto\"><div width=100px height=100px><a ng-click=\"showExibirImagensImovel($event, item.fotos)\"><img ng-src=\"{{item.fotos.length > 0 ? gerarImagem(item.fotos[0].arquivo.binario) : \'http://i.imgur.com/YV0WS3k.png\'}}\" style=\"max-height: 100px; max-width: 100px\"></a></div></div></td><td md-cell><span ng-bind=listaTiposAmostraTab[item.tipoAmostra]></span></td><td md-cell><span ng-bind=item.informanteFonte></span></td><td md-cell><span ng-if=!item.enderecamento.logradouro ng-bind=\"(item.enderecamento.cep | cep)\"></span> <span ng-if=item.enderecamento.logradouro ng-bind=\"item.enderecamento.logradouro + \', Número \'\n                                                        + item.enderecamento.numero + \', \'\n                                                        + item.enderecamento.bairro + \', \'\n                                                        + item.enderecamento.municipio + \'/\'\n                                                        + item.enderecamento.uf + \' - \'\n                                                        + (item.enderecamento.cep | cep)\"></span></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=completarDadosAmostra(item.id)><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"excluirAmostra($index, item.id)\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoDadosAmostra md-page-select></md-table-pagination></div></div><div class=\"m-t-5 m-l-10\" layout-align=\"start start\" flex=100 layout=row><md-checkbox class=\"md-primary legenda-pendencia-preenchimento\" ng-disabled=true><label class=text-black>{{\'label-registro-pendencia-preenchimento\' | translate}}</label></md-checkbox></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=fecharUploadPlanilhaAmostra()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarRascunhoAmostra()><label>{{\'button-salvar-rascunho\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=adicionarBaseDadosAmostra()><label>{{\'button-adicionar-registros-base-dados-mercado\' | translate}}</label></md-button></div></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/area-equivalente/views/calcularAreaEquivalente.html","<form name=formularioDados><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'label-calcular-area-equivalente\' | translate}}<md-icon ng-click=showDialogCalculoAreaEquivalente($event) ng-bind=\"\'info\'\" class=\"m-b-5 mouse-pointer md-fab md-raised md-mini\"></md-icon></h2></div></div><div layout=column flex><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-benfeitoria\' | translate}}:</span></span></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-area\' | translate}}:</span></span></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50><div><b><span>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</span></b></div></md-input-container><md-input-container class=md-block flex=50><div><b><span>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</span></b></div></md-input-container></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm class=m-t-20><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'label-area-construida-padrao-ap\' | translate}}:</label><input ng-disabled=detalhar ng-model=areaEquivalente.areaConstruidaPadrao ui-mask-placeholder-char=space ui-number-mask=2 ng-blur=changeAreaConstruidaPadrao();></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-cub-area-padrao\' | translate}}</label><md-select ng-disabled=detalhar ng-model=areaEquivalente.cubPadrao ng-change=changeCubPadrao()><md-option ng-repeat=\"cub in tiposCub\" ng-value=cub.nome>{{cub.nome}}</md-option></md-select></md-input-container></div><div ng-show=\"areaEquivalente.cubPadrao != null\"><div layout=row layout-xs=row flex=100 flex-xs flex-sm class=m-t-20><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'label-descricao-area-diferente-padrao-construtivo\' | translate}}:</label><input ng-disabled=detalhar type=text maxlength=100 ng-model=descricaoAreaConstruidaPadraoDiferente ui-mask-placeholder-char=space></md-input-container><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'label-cub-area-padrao-diferente-aq\' | translate}}:</label><input ng-disabled=detalhar ng-model=areaConstruidaPadraoDiferente ui-mask-placeholder-char=space ui-number-mask=2 ng-blur=changeAreaConstruidaPadraoDiferente();></md-input-container></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=33 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-coeficiente\' | translate}}:*</span></span></div><div flex=40></div><div class=md-block flex-gt-xs flex=33 style=\"margin-top: -15px\" ng-show=\"tipoCoeficiente == \'CALCULAR\'\"><span class=datepicker-label><span>{{\'label-coeficiente-calculado\' | translate}}:*</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div layout=column flex=33><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=tipoCoeficiente ng-disabled=detalhar ng-change=changeTipoCoeficiente()><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=30 ng-value=\"\'CALCULAR\'\"><div class=texto-preto>{{\'label-calcular\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=20 ng-value=\"\'INFORMAR\'\"><div class=texto-preto>{{\'label-informar\' | translate}}</div></md-radio-button></div></md-radio-group></div><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 ng-show=\"tipoCoeficiente == \'CALCULAR\'\"><label>{{\'label-cub-padrao-difente\' | translate}}*</label><md-select ng-disabled=detalhar ng-model=cubPadraoDiferente ng-change=changeCubPadraoDiferente()><md-option ng-repeat=\"cub in tiposCubPadraoDiferente\" ng-value=cub.nome>{{cub.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 ng-show=\"tipoCoeficiente == \'CALCULAR\'\"><div><b><span>{{(valorCoeficiente | currency).replace(\'R$\', \'\')}}</span></b></div></md-input-container><md-input-container layout-xs=row flex=33 flex-xs flex-sm class=md-block ng-show=\"tipoCoeficiente == \'INFORMAR\'\"><label>{{\'label-coeficiente-p\' | translate}}:*</label><input ng-disabled=detalhar ng-model=valorCoeficiente ui-mask-placeholder-char=space ui-number-mask=2></md-input-container><md-input-container layout-xs=row flex=33 flex-xs flex-sm class=md-block ng-show=\"tipoCoeficiente == \'INFORMAR\'\"><label>{{\'label-fonte\' | translate}}:</label><input ng-disabled=detalhar type=text maxlength=100 ng-model=fonteCoeficiente ui-mask-placeholder-char=space></md-input-container></div><div class=\"md-block m-b-20\" flex-gt-xs flex=100><div layout=row layout-align=end><div ng-show=\"!detalhar && !editar.buttonShow\"><md-button class=\"md-primary md-raised\" ng-click=adicionarArea() style=\"font-weight:normal; text-transform:none\"><label>{{\'button-adicionar\' | translate}}</label></md-button></div><div ng-show=\"!detalhar && editar.buttonShow\"><md-button class=\"md-primary md-raised\" ng-click=gravarAlteracaoArea() style=\"font-weight:normal; text-transform:none\"><label>{{\'button-editar\' | translate}}</label></md-button></div></div></div><div flex class=caixa-tabela-resultado ng-show=\"areaEquivalente.areasPadraoDiferente.length > 0\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-descricao\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 30%\" class=th-margin-pvgs md-column>{{\'label-coeficiente-calculo-area-equivalente\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area-equivalente-calculada\' | translate}}</th><th style=\"width: 10%\" ng-show=!detalhar class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in areaEquivalente.areasPadraoDiferente\"><td md-cell>{{item.descricaoAreaConstruidaPadraoDiferente}}</td><td md-cell>{{(item.areaConstruidaPadraoDiferente | currency).replace(\'R$\', \'\')}}</td><td md-cell ng-show=\"item.tipoCoeficiente == \'CALCULAR\'\">{{\'label-calculado-com-cub\' | translate}} - {{item.cubPadraoDiferente}}<br>{{\'label-coeficiente\' | translate}} - {{(item.valorCoeficiente | currency).replace(\'R$\', \'\')}}</td><td md-cell ng-show=\"item.tipoCoeficiente == \'INFORMAR\'\">{{\'label-fonte\' | translate}} - {{item.fonteCoeficiente}}<br>{{\'label-coeficiente\' | translate}} - {{(item.valorCoeficiente | currency).replace(\'R$\', \'\')}}</td><td md-cell>{{(item.areaEquivalenteCalculada | currency).replace(\'R$\', \'\')}}</td><td md-cell ng-show=!detalhar><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=alterarArea($index) class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-editar\' | translate}}\" ng-bind=\"\'edit\'\" aria-label=\"{{\'label-editar\' | translate}}\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=removerArea($index) class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir\' | translate}}\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container></md-content></div></div><div class=\"m-t-10 m-b-10\" layout=row layout-xs=column ng-show=areaEquivalente.areaEquivalenteConstrucao><md-input-container class=md-block flex=50><div><span><b>{{\'label-area-equivalente-construcao\' | translate}}:</b>&nbsp;{{(areaEquivalente.areaEquivalenteConstrucao | currency).replace(\'R$\', \'\')}}</span></div></md-input-container></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=fecharCalcularAreaEquivalente()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=salvar() ng-show=!detalhar><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/avaliacao/language/pt-BR.json","{ \"label-avaliacao\": \"AVALIAÇÃO\", \"label-contabilidade\": \"CONTABILIDADE\", \"label-imovel\": \"Imóvel\", \"label-valor-referencia-terreno\": \"Valor Referência Terreno\", \"label-avaliacao-individualizada\": \"Avaliação Individualizada\", \"label-tipo-logradouro\": \"Tipo Logradouro\", \"label-ver-dados-imovel\": \"Ver dados do Imóvel\", \"label-ver-dados-destinacao-imovel\": \"Ver dados da destinação do Imóvel\", \"button-confirmar-rip\": \"Confirmar RIP\", \"mensagem-rip-invalido\": \"RIP inválido\", \"mensagem-imovel-nao-encontrado\": \"Imóvel não encontrado\", \"button-fechar\":\"Fechar\", \"titulo-dados-contabeis\": \"Avaliação - dados contábeis\", \"label-vigencia\": \"Vigência\", \"label-valor\": \"Valor\", \"label-data-assinatura\": \"Data de Assinatura\", \"label-modalidade\": \"Modalidade\", \"avaliacao-vencida\": \"(Vencida)\" }");
$templateCache.put("scripts-avaliacao/pages/avaliacao/views/detalharImovelAvaliacao.html","<div style=\"margin-top:0px !important\" layout=column flex=auto><div ng-cloak><md-content><md-content class=md-padding><md-nav-bar md-no-ink-bar=disableInkBar md-selected-nav-item=\"\'avaliacao\'\" nav-bar-aria-label=\"navigation links\"><md-nav-item md-nav-click=gotoAvaliacao() name=avaliacao>{{\'label-avaliacao\' | translate}}</md-nav-item><!--<md-nav-item md-nav-click=\"gotoContabilidade()\" name=\"contabilidade\">\n                        {{\'label-contabilidade\' | translate}}\n                    </md-nav-item>--></md-nav-bar></md-content></md-content><md-content class=md-padding style=\"overflow: hidden\"><div layout=row><div layout=column flex=30><fieldset class=fieldset style=\"margin-right: 10px\"><legend>{{\'label-imovel\' | translate}}</legend><div class=m-t-20 style=\"text-align: left\" layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-rip\' | translate}}</label><input ng-model=ripConsultar maxlength=8 name=\"{{nomeCampo}} - {{\'label-rip\' | translate}}\" ng-keyup=\"$event.keyCode === 13 && consultarImovelPeloRipInformado()\"></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100 class=rip-link><a href=\"\" ng-click=redirecionarConsultarImovelLaudo() ng-show=!isConsultarRip style=font-weight:normal>{{\'label-nao-sei-rip\'| translate}}</a></md-input-container><div layout=column layout-xs=column layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=consultarImovelPeloRipInformado() ng-disabled=isConsultarRip><label style=\"font-weight:normal; text-transform:none\">{{\'button-confirmar-rip\' | translate}}</label></md-button></div></div><div layout=row ng-if=dadoContabil><div layout=column style=\"text-align: left\"><div class=m-b-5><span>{{\'titulo-dados-contabeis\' | translate}}</span> <span class=\"mensagemErro m-l-10\" ng-if=dadoContabil.vencido>{{\'avaliacao-vencida\' | translate}}</span></div><div ng-if=!dadoContabil.vencido><div class=m-b-5><span>{{\'label-vigencia\' | translate}}:</span> <span class=texto-simples>{{dadoContabil.dataFimVigencia | date: \'dd/MM/yyyy\' : \'-0300\'}}</span></div><div class=m-b-5><span>{{\'label-valor\' | translate}}:</span> <span class=texto-simples>{{dadoContabil.valor | currency:\'R$\'}}</span></div><div class=m-b-5><span>{{\'label-data-assinatura\' | translate}}:</span> <span class=texto-simples>{{dadoContabil.dataInicioVigencia | date: \'dd/MM/yyyy\' : \'-0300\'}}</span></div><div class=m-b-5><span>{{\'label-modalidade\' | translate}}:</span> <span class=texto-simples>{{dadoContabil.modalidade}}</span></div></div></div></div><div class=m-t-20 style=\"text-align: left\" layout=row layout-xs=column ng-hide=!ripPreenchido><a href=\"\" ng-click=ripImovelLink(ripConsultar) target=_blank style=font-weight:normal>{{\'label-ver-dados-imovel\'| translate}}</a></div><!-- Exibir este trecho apenas quando for ambiente de desenvolvimento--><div ng-style=\"ambienteDev ? {\'background-color\': \'rgb(249, 242, 214)\'} : {}\" ng-if=ambienteDev class=m-t-20 style=\"text-align: left\" layout=row layout-xs=column><a href=\"\" ng-click=dadosUtilizacaoLink(ripConsultar) target=_blank style=font-weight:normal>{{\'label-ver-dados-destinacao-imovel\'| translate}}</a></div><div style=\"text-align: left; font-weight:normal\" ng-show=imovel.dsSpiunet layout=row layout-xs=column><label>{{\'rip-spiunet\' | translate}} {{imovel.dsSpiunet | ripSpiuNet}}</label></div></fieldset><area-lateral-mapa-slick ng-if=rip rip=rip ponto-imovel=imovel.pontoImovel endereco=enderecoImovel capturar-mapa=null></area-lateral-mapa-slick></div><div layout=column flex=70><md-tabs md-border-bottom md-dynamic-height ng-if=rip><!-- Exibir este trecho apenas quando for ambiente de desenvolvimento--><md-tab ng-if=ambienteDev label=\"{{\'label-valor-referencia-terreno\' | translate}}\" md-selected=tabs.selectedIndex md-active=\"tabs.selectedIndex==0\"><div ng-style=\"ambienteDev ? {\'background-color\': \'rgb(249, 242, 214)\'} : {}\" layout=row layout-xs=column><cadastro-pvgs rip=rip imovel=imovel area-terreno=areaTerreno flex></cadastro-pvgs></div></md-tab><md-tab label=\"{{\'label-avaliacao-individualizada\' | translate}}\" id=avaliacaoIndividualizada md-selected=tabs.selectedIndex md-active=\"ambieneDev? tabs.selectedIndex==1 : tabs.selectedIndex==0\"><div layout=row layout-xs=column><cadastro-laudo-unidade-autonoma rip=rip uf=imovel.enderecamento.uf flex></cadastro-laudo-unidade-autonoma></div></md-tab></md-tabs></div></div></md-content></div></div><div layout=row layout-xs=column class=\"buttons-action m-t-20\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ui-sref=avaliacao.CONSULTAR_DADOS_AVALIACAO_IMOVEL><label>{{\'button-fechar\' | translate}}</label></md-button></div></div></div>");
$templateCache.put("scripts-avaliacao/pages/avaliacao/views/detalharImovelContabilidade.html","<div style=\"margin-top:0px !important\" layout=column flex=auto><div ng-cloak><md-content><md-content class=md-padding><md-nav-bar md-no-ink-bar=disableInkBar md-selected-nav-item=\"\'contabilidade\'\" nav-bar-aria-label=\"navigation links\"><md-nav-item md-nav-click=gotoAvaliacao() name=avaliacao>{{\'label-avaliacao\' | translate}}</md-nav-item><md-nav-item md-nav-click=gotoContabilidade() name=contabilidade>{{\'label-contabilidade\' | translate}}</md-nav-item></md-nav-bar></md-content></md-content><md-content class=md-padding><div layout=row><div layout=column flex=30><fieldset class=fieldset style=\"margin-right: 10px\"><legend>{{\'label-imovel\' | translate}}</legend><div class=m-t-20 style=\"text-align: left\" layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-rip\' | translate}}</label><input ng-model=ripConsultar maxlength=8 name=\"{{nomeCampo}} - {{\'label-rip\' | translate}}\"></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100 class=rip-link><a href=\"\" ng-click=redirecionarConsultarImovelLaudo() ng-show=!isConsultarRip style=font-weight:normal>{{\'label-nao-sei-rip\'| translate}}</a></md-input-container></div><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=consultarImovelPeloRipInformado() ng-disabled=isConsultarRip><label style=\"font-weight:normal; text-transform:none\">{{\'button-confirmar-rip\' | translate}}</label></md-button></div><!-- Exibir este trecho apenas quando for ambiente de desenvolvimento--><div ng-style=\"ambienteDev ? {\'background-color\': \'rgb(249, 242, 214)\'} : {}\" ng-if=ambienteDev class=m-t-20 style=\"text-align: left\" layout=row layout-xs=column><a href=\"\" ng-click=ripImovelLink(ripConsultar) target=_blank style=font-weight:normal>{{\'label-ver-dados-imovel\'| translate}}</a></div><div ng-style=\"ambienteDev ? {\'background-color\': \'rgb(249, 242, 214)\'} : {}\" ng-if=ambienteDev class=m-t-20 style=\"text-align: left\" layout=row layout-xs=column><a href=\"\" ng-click=dadosUtilizacaoLink(ripConsultar) target=_blank style=font-weight:normal>{{\'label-ver-dados-destinacao-imovel\'| translate}}</a></div></fieldset><area-lateral-mapa-slick ng-if=rip rip=rip ponto-imovel=imovel.pontoImovel capturar-mapa=null></area-lateral-mapa-slick></div><div layout=column flex=70><div class=row><div layout=column flex=100 class=block-danger ng-show=showAvisoMoeda><div class=title><md-icon md-font-set=material-icons class=step>error_outline</md-icon><span>Atenção</span></div><p>Há valores em outra moeda para este imóvel. Para verificar os dados anteriores, selecione a opção desejada no campo abaixo:</p><div layout=row layout-xs=column><md-input-container class=md-block flex=40><label></label><md-select ng-model=pesquisaHistorico.moedaDataAtualizacao ng-change=changeMoeda() name=teste><md-option ng-repeat=\"item in dadosContabeis.data\" ng-value=item.dataAtualizacao>{{item.moeda.nome + \' - \' + (item.dataAtualizacao | date: \'MM/yyyy\')}}</md-option></md-select></md-input-container></div></div><div layout=column flex=100><p>Valor contábil: <strong>{{((dadosContabeis.data[0].valor | currency) || \'--------\').replace(\'R$\', \'\')}}</strong><br>Data da última atualização: <strong>{{dadosContabeis.data[0].dataAtualizacao | date: \'dd/MM/yyyy\' : \'-0300\'}}</strong></p></div><div layout=column flex=100><h2 class=subtitulo-pagina>Histórico do Valor Contábil</h2></div><form name=formPesquisaHistorico><div layout=row><div layout=column flex=33><div style=\"margin-top: -15px\"><span class=datepicker-label><span>Periodo de Apresentação:*</span></span></div><md-container layout=column><div flex=40 layout=row layout-xs=row><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=pesquisaHistorico.dataInicial md-max-date=hoje ng-required=true></md-datepicker></div><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=pesquisaHistorico.dataFinal md-max-date=hoje ng-required=true></md-datepicker></div></div></md-container></div><div layout=column flex=25 style=\"padding-left: 10px\"><div style=\"margin-top: -15px\"><span class=datepicker-label><span>Visualização do Valor Contábil:*</span></span></div><md-radio-group flex=100 class=tamanho-radio-button-avaliacao ng-model=pesquisaHistorico.tipo name=pesquisaTipo><div layout=row><md-radio-button layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=\"\'tabela\'\"><div class=texto-preto>Tabela</div></md-radio-button><!-- Exibir este trecho apenas quando for ambiente de desenvolvimento--><md-radio-button ng-style=\"ambienteDev ? {\'background-color\': \'rgb(249, 242, 214)\'} : {}\" ng-if=ambienteDev layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=\"\'grafico\'\"><div class=texto-preto>Gráfico</div></md-radio-button></div></md-radio-group></div><div layout=column flex=50><div style=\"margin-top: -10px\"><md-button ng-click=buscarHistorico(); class=\"md-primary md-raised pull-right\" type=button><label>Visualizar histórico de valor contábil</label></md-button></div></div></div></form><div layout=row ng-if=\"dadosContabeis && dadosContabeis.tipo == \'tabela\' && dadosContabeis.data.length > 0\"><div flex=\"\" style=\"position: relative\"><div class=table-scroll style=\"width: calc(100% - 255px); margin-left: 17.8em\"><table><tr class=header><th class=headcol style=\"width: 17em\"></th><td ng-repeat=\"item in dadosContabeis.data\">{{item.dataAtualizacao | date: \'dd/MM/yyyy\' : \'-0300\'}}</td></tr><tr><th class=headcol style=\"width: 17em;padding: 20px 5px\">Evento</th><td ng-repeat=\"item in dadosContabeis.data\"><span ng-if=\"item.tipo == \'LAUDO_AVALIACAO-VIGENTE\' || item.tipo == \'LAUDO_AVALIACAO-ATIVO\' || item.tipo == \'LAUDO_RELATORIO_VALOR_REFERENCIA\'\"><md-button ng-click=gotoLaudo(item.id)>{{getCuteName(item.tipo)}}</md-button></span><span ng-if=\"item.tipo == \'DOMÍNIO_PLENO\'\"><md-button>{{getCuteName(item.tipo)}}</md-button></span><span ng-if=\"item.tipo == \'VALOR_CONTÁBIL\'\"><md-button ng-click=\"dialogDetalhesEvento($event, item)\">{{getCuteName(item.tipo)}}</md-button></span></td></tr><tr><th class=headcol style=\"width: 17em\">Valor Total</th><td ng-repeat=\"item in dadosContabeis.data\">{{((item.valor | currency) || \'--------\').replace(\'R$\', \'\')}}</td></tr><tr ng-if=dadosContabeis.data[0].benfeitorias><th class=headcol style=\"width: 17em\">Valor do Terreno</th><td ng-repeat=\"item in dadosContabeis.data\">{{((item.valorTerreno | currency) || \'--------\').replace(\'R$\', \'\')}}</td></tr><tr ng-init=\"parentIndex = $index\" ng-repeat=\"row in dadosContabeis.data[0].benfeitorias\"><th class=headcol style=\"width: 17em\">Benfeitoria [{{row.codigo}}]</th><td ng-repeat=\"item in dadosContabeis.data\">{{((item.benfeitorias[parentIndex].valor | currency) || \'--------\').replace(\'R$\', \'\')}}</td></tr><tr ng-if=!dadosContabeis.data[0].benfeitorias><th class=headcol style=\"width: 17em\">Valor da Fração Ideal do Terreno</th><td ng-repeat=\"item in dadosContabeis.data\">{{((item.valorContabilUnidadeAutonoma | currency) || \'--------\').replace(\'R$\', \'\')}}</td></tr><tr ng-if=!dadosContabeis.data[0].benfeitorias><th class=headcol style=\"width: 17em\">Valor da Unidade Autônoma</th><td ng-repeat=\"item in dadosContabeis.data\">{{((item.valorContabilUnidadeAutonoma | currency) || \'--------\').replace(\'R$\', \'\')}}</td></tr></table></div></div></div><div layout=row ng-if=\"dadosContabeis && dadosContabeis.tipo == \'grafico\' && dadosContabeis.data.length > 0\"><div id=chart style=\"height: 250px\" layout=column flex=100></div></div></div><!-- Exibir este trecho apenas quando for ambiente de desenvolvimento--><div ng-style=\"ambienteDev ? {\'background-color\': \'rgb(249, 242, 214)\'} : {}\" ng-if=ambienteDev class=row ng-if=dadosDepreciacao><div layout=column flex=100><h2 class=subtitulo-pagina>Depreciação</h2></div><form name=formPesquisaDepreciacao><div layout=row><div layout=column flex=25><div style=\"margin-top: -15px\"><span class=datepicker-label><span>Depreciação:*</span></span></div><md-radio-group flex=100 class=tamanho-radio-button-avaliacao ng-model=pesquisaDepreciacao.modo name=pesquisaTipo><div layout=row><md-radio-button layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=\"\'acumulada\'\"><div class=texto-preto>Acumulada</div></md-radio-button><md-radio-button layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=\"\'mensal\'\"><div class=texto-preto>Mensal</div></md-radio-button></div></md-radio-group></div></div><div layout=row><div layout=column flex=33><div style=\"margin-top: -15px\"><span class=datepicker-label><span>Periodo de Apresentação:*</span></span></div><md-container layout=column><div flex=40 layout=row layout-xs=row><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=pesquisaDepreciacao.dataInicial md-max-date=hoje ng-required=true></md-datepicker></div><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=pesquisaDepreciacao.dataFinal md-max-date=hoje ng-required=true></md-datepicker></div></div></md-container></div><div layout=column flex=25 style=\"padding-left: 10px\"><div style=\"margin-top: -15px\"><span class=datepicker-label><span>Visualização da Depreciação:*</span></span></div><md-radio-group flex=100 class=tamanho-radio-button-avaliacao ng-model=pesquisaDepreciacao.tipo name=pesquisaTipo><div layout=row><md-radio-button layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=\"\'tabela\'\"><div class=texto-preto>Tabela</div></md-radio-button><md-radio-button layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=\"\'grafico\'\"><div class=texto-preto>Gráfico</div></md-radio-button></div></md-radio-group></div><div layout=column flex=50><div style=\"margin-top: -10px\"><md-button ng-click=buscarDepreciacao(); class=\"md-primary md-raised pull-right\" type=button><label>Visualizar dados da depreciação</label></md-button></div></div></div></form><div layout=row ng-if=\"dadosDepreciacao && dadosDepreciacao.tipo == \'tabela\' && dadosDepreciacao.data.length > 0\"><div flex=\"\" style=\"position: relative\"><div class=table-scroll><table><tr class=header><th class=headcol></th><td ng-repeat=\"item in dadosDepreciacao.header\">{{item}}</td></tr><tr ng-repeat=\"row in dadosDepreciacao.data\"><th ng-show=\"$index == 0 && row[0].benfeitoriaImovel\" class=headcol>Benfeitoria {{row[0].benfeitoriaImovel.codigo}}</th><th ng-show=\"$index == 0 && !row[0].benfeitoriaImovel\" class=headcol>Unidade Autônoma</th><td style=\"cursor: pointer\" ng-click=\"dialogDetalhesBenfeitoriaDepreciacao($event, item)\" ng-repeat=\"item in row\">{{dadosDepreciacao.modo == \'acumulada\' ? item.depreciacaoAcumulada || \'-------\' : item.depreciacaoMensal || \'-------\' | number: \'2\'}}</td></tr></table></div></div></div><div layout=row ng-if=\"dadosDepreciacao && dadosDepreciacao.tipo == \'grafico\' && dadosDepreciacao.data.length > 0\"><div id=chart2 style=\"height: 250px\" layout=column flex=100></div></div></div></div></div></md-content></div></div><div layout=row layout-xs=column class=\"buttons-action m-t-20\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ui-sref=avaliacao.CONSULTAR_DADOS_AVALIACAO_IMOVEL><label>{{\'button-fechar\' | translate}}</label></md-button></div></div></div><style>div.table-scroll table {\n        border-collapse: separate;\n        border-top: 1px solid grey;\n    }\n\n    div.table-scroll td, th {\n        margin: 0;\n        border: 1px solid grey;\n        border-top-width: 0;\n        white-space: nowrap;\n    }\n\n    div.table-scroll {\n        width: calc(100% - 193px);\n        overflow-x: scroll;\n        margin-left: 13.8em;\n        overflow-y: visible;\n        padding-bottom: 1px;\n    }\n\n    div.table-scroll tr:first-child .headcol {\n        margin-top: -1px;\n        height: 19px;\n    }\n\n    .headcol {\n        position: absolute;\n        width: 13em;\n        left: 0;\n        color: #333 !important;\n        top: auto;\n        border-right: 0 none black;\n        border-top-width: 1px; /*only relevant for first row*/\n        margin-top: 0; /*compensate for top border*/\n        text-align: right !important;\n    }\n\n    div.table-scroll .header {\n        background: #eee;\n        font-weight: 600;\n    }</style>");
$templateCache.put("scripts-avaliacao/pages/cub/language/pt-BR.json","{ \"label-tipo\": \"Tipo\", \"label-descricao\": \"Descrição\", \"label-area-construida\": \"Área Construida (m²)\", \"label-vida-util\": \"Vida Útil (Ano)\", \"label-idade-aparente\": \"Idade Aparente (Ano)\", \"label-padrao-de-acabamento\": \"Padrão de Acabamento\", \"label-tipo-de-projeto\": \"Tipo de Projeto\", \"label-cub\": \"CUB\", \"label-fator\": \"Fator\", \"label-fatores-acrescimos\": \"Fatores de acréscimos ao CUB\", \"label-especificadao-do-fator\": \"Especificação do fator\", \"label-porcentagem\": \"Porcentagem\", \"label-justificativa-fonte\": \"Justificativa/Fonte\", \"label-valor-estimado\": \"Valor Estimado\", \"label-estado-conservacao\": \"Estado de Conservação\", \"label-valor-cub\": \"Valor do CUB (m²)\", \"label-valor-cub-depreciado\": \"Valor do CUB Depreciado (m²)\", \"label-inserir-fatores-acrescimo-cub\": \"Inserir Fatores de Acréscimo ao CUB\", \"button-adicionar\": \"Adicionar\", \"button-gravar\": \"Gravar\", \"button-editar\": \"Editar\", \"button-fechar\": \"Fechar\", \"button-limpar\": \"limpar\", \"tr-fator\": \"Fator\", \"tr-porcentagem\": \"Porcentagem\", \"tr-acoes\": \"Ações\", \"alt-editar\": \"Editar\", \"alt-excluir\": \"Excluir\", \"mensagem-nenhum-valor-encontrado\": \"Deseja cadastrar o CUB?\", \"title-nenhum-valor-encontrado\": \"Nenhum valor encontrado para o CUB selecionado\" }");
$templateCache.put("scripts-avaliacao/pages/cub/views/informarValorCub.html","<form name=formularioDados><div layout=row layout-xs=column ng-if=\"tipoTerreno==TIPO_BENFEITORIA\"><div flex><h2 class=titulo-pagina ng-bind=\"(\'title-benfeitoria\' | translate) + \' \'\n                                + benfeitoria.codigo + \' - \'\n                                + benfeitoria.tipoBenfeitoria.descricao + \'/\'\n                                + (benfeitoria.edificacao ? benfeitoria.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></h2></div></div><div layout=row layout-align=\"start center\" layout-xs=column layout-align-xs=\"start start\" layout-sm=column layout-align-sm=\"start start\" layout-md=column layout-align-md=\"start start\" flex=100 ng-if=\"tipoTerreno==TIPO_BENFEITORIA\"><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-tipo\' | translate}}:</div><div layout=row><label ng-bind=benfeitoria.tipoBenfeitoria.descricao></label></div></div><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-descricao\' | translate}}:</div><div layout=row><label ng-bind=\"(benfeitoria.edificacao ? benfeitoria.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></div></div><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-area-construida\' | translate}}:</div><div layout=row><label ng-bind=\"(benfeitoria.areaConstruida | currency).replace(\'R$\',\'\')\"></label></div></div></div><!--###################### BENFEITORIAS ##########################--><!--###################### UNIDADEAUTONOMA ##########################--><div class=separarPaddingRows layout=row flex layout-align=\"start start\" ng-if=\"tipoTerreno==TIPO_UNIDADE_AUTONOMA\"><h2><label ng-bind=\"unidadeAutonomaImovel[0].edificacao.tipoEdificacao.descricao + \'/\'\n                                + unidadeAutonomaImovel[0].edificacao.tipoProjeto.descricao\"></label></h2></div><div layout=row layout-align=\"start center\" layout-xs=column layout-align-xs=\"start start\" layout-sm=column layout-align-sm=\"start start\" layout-md=column layout-align-md=\"start start\" flex=100 ng-if=\"tipoTerreno==TIPO_UNIDADE_AUTONOMA\"><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-tipo\' | translate}}:</div><div layout=row><label ng-bind=unidadeAutonomaImovel[0].edificacao.tipoEdificacao.descricao></label></div></div><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-descricao\' | translate}}:</div><div layout=row><label ng-bind=unidadeAutonomaImovel[0].edificacao.tipoProjeto.descricao></label></div></div><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-area-construida\' | translate}}:</div><div layout=row><label ng-bind=\"(unidadeAutonomaImovel[0].area | currency).replace(\'R$\',\'\')\"></label></div></div></div><!--###################### UNIDADEAUTONOMA ##########################--><div layout=row layout-xs=column class=m-t-20><md-input-container class=md-block flex=50 flex-sm=100 flex-xs=100><label>{{\'label-vida-util-ano\' | translate}}</label><input type=text maxlength=3 ui-number-mask=0 min=1 max=999 ng-model=valoresTemp.vidaUtil ng-change=calcularValorEstimado() name=\"{{nomeCampo}} - {{\'label-vida-util-ano\' | translate}}\" required></md-input-container><md-input-container class=md-block flex=50 flex-sm=100 flex-xs=100><label>{{\'label-idade-aparente-ano\' | translate}}</label><input type=text maxlength=3 ui-number-mask=0 min=0 max=999 ng-model=valoresTemp.idadeAparente ng-change=calcularValorEstimado() name=\"{{nomeCampo}} - Idade Aparente\" required></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-cub\' | translate}}</label><md-select ng-model=valoresTemp.cubSelecionado required ng-change=\"buscarValoresCubSiapa(valoresTemp.cubSelecionado, 0)\"><md-option ng-repeat=\"cub in tiposCub\" ng-value=cub>{{cub.nome}}</md-option></md-select></md-input-container><md-input-container class=\"md-block button-info-right\" flex=50 flex-xs=100 flex-sm=100><label>{{\'label-estado-conservacao\' | translate}}</label><md-select ng-model=valoresTemp.estadoConservacao required ng-change=calcularValorEstimado()><md-option ng-repeat=\"item in listaEstadosConservacao\" ng-value=item>{{item.nome}}</md-option></md-select><md-button ng-click=showDialogDepreciacaoHeidecke($event) style=\"position: absolute\"><md-icon flex=10 ng-bind=\"\'info\'\" class=\"md-fab md-raised md-mini\"><md-tooltip>Depreciação de Heidecke</md-tooltip></md-icon></md-button></md-input-container></div><div class=\"m-t-30 m-b-30\" layout=row layout-xs=column flex=100><div flex=100 flex-sm=100 flex-xs=100><div layout=row>{{\'label-valor-cub\' | translate}}: <span class=msg-erro-cub>{{msgErro}}</span></div><div layout=row><label ng-bind=\"(valoresTemp.valorCub | currency).replace(\'R$\',\'\')\"></label></div></div></div><div class=\"m-t-30 m-b-30\" layout=row layout-xs=column flex=100 ng-show=\"laudoAvaliacao.tipoValorLaudo == ESTIMAR_VALORES\"><div flex=100 flex-sm=100 flex-xs=100><div layout=row>{{\'label-valor-cub-depreciado\' | translate}}:</div><div layout=row><label ng-bind=\"(valoresTemp.valorCubDepreciado | currency).replace(\'R$\',\'\')\"></label></div></div></div><div ng-show=\"laudoAvaliacao.tipoValorLaudo == ESTIMAR_VALORES\"><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=valoresTemp.isFatoresAcrescimo ng-change=limparDadosFatoresAcrescimo(); name=\"{{\'label-inserir-fatores-acrescimo-cub\' | translate}}\"><label class=text-black>{{\'label-inserir-fatores-acrescimo-cub\' | translate}}</label></md-checkbox></div><div ng-show=valoresTemp.isFatoresAcrescimo><div layout=row layout-xs=column><h3 class=subtitulo-pagina>Fatores</h3></div><div layout=row layout-xs=column layout-sm=column flex=100><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-fatores-acrescimos\' | translate}}*</label><md-select ng-model=estimativa.motivo ng-change=\"estimativa.especificacao = null\"><md-option ng-repeat=\"(k, v) in listaMotivosEstimativa\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-input-container ng-show=\"estimativa.motivo == \'OUTRO\'\" class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-especificadao-do-fator\' | translate}}*</label><input name=\"{{\'label-especificadao-do-fator\' | translate}}\" ng-model=estimativa.especificacao></md-input-container><md-input-container class=md-block flex=\"estimativa.motivo == \'OUTRO\' ? \'33\' : \'66\" flex-xs=100 flex-sm=100><label>{{\'label-porcentagem\' | translate}}*</label><input name=\"{{\'label-porcentagem\' | translate}}\" ng-model=estimativa.porcentagem ui-percentage-mask=2></md-input-container></div><div layout=row layout-xs=column flex=100><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-justificativa-fonte\' | translate}}*</label><textarea rows=1 cols=50 name=\"{{\'label-justificativa-fonte\' | translate}}\" ng-model=estimativa.justificativaFonte></textarea></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" ng-click=limparEstimativaValorCub()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button ng-show=!buttonEditarEstimativa class=\"md-primary md-raised m-l-15\" ng-click=adicionarEstimativaValorCub()><label>{{\'button-adicionar\' | translate}}</label></md-button><md-button ng-show=buttonEditarEstimativa class=\"md-primary md-raised m-l-15\" ng-click=adicionarEstimativaValorCub()><label>{{\'button-editar\' | translate}}</label></md-button></div></div></div><div layout=row layout-align=\"center start\" class=m-t-20 ng-show=\"paginacaoEstimativasValores.length > 0\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 15%\" class=th-margin-pvgs md-column>{{\'tr-fator\' | translate}}</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'tr-porcentagem\' | translate}}</th><th style=\"width: 35%\" class=th-margin-pvgs md-column>{{\'label-especificadao-do-fator\' | translate}}</th><th style=\"width: 35%\" class=th-margin-pvgs md-column>{{\'label-justificativa-fonte\' | translate}}</th><th style=\"width: 5%\" class=th-margin-pvgs md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"estimativaValor in paginacaoEstimativasValores \"><td md-cell><label ng-bind=listaMotivosEstimativa[estimativaValor.motivo]></label></td><td md-cell><label ng-bind=\"(estimativaValor.porcentagem | percentage)\"></label></td><td md-cell><label class=descricao-tabela>{{estimativaValor.especificacao}}</label><a href=\"\" target=_blank class=btn-ver-mais ng-show=estimativaValor.especificacao>ver mais</a><div><script>$(\".btn-ver-mais\").unbind(\'click\');</script></div><div ng-if=\"paginacaoEstimativasValores.length - 1 == $index\"><script>$(\".btn-ver-mais\").on(\'click\', function () {\n                                                if ($(this).parent().find(\".descricao-tabela\").length > 0) {\n                                                    var content = $(this).parent().find(\".descricao-tabela\");\n                                                    content.removeClass(\"descricao-tabela\");\n                                                    $(this).html(\"ver menos\");\n                                                } else {\n                                                    var content = $(this).parent().find(\"label\");\n                                                    content.addClass(\"descricao-tabela\");\n                                                    $(this).html(\"ver mais\");\n                                                }\n                                            })</script></div></td><td md-cell><label class=justificativa-tabela>{{estimativaValor.justificativaFonte}}</label><a href=\"\" target=_blank class=btn-ver-mais-justificativa>ver mais</a><div><script>$(\".btn-ver-mais-justificativa\").unbind(\'click\');</script></div><div ng-if=\"paginacaoEstimativasValores.length - 1 == $index\"><script>$(\".btn-ver-mais-justificativa\").on(\'click\', function () {\n                                                if ($(this).parent().find(\".justificativa-tabela\").length > 0) {\n                                                    var content = $(this).parent().find(\".justificativa-tabela\");\n                                                    content.removeClass(\"justificativa-tabela\");\n                                                    $(this).html(\"ver menos\");\n                                                } else {\n                                                    var content = $(this).parent().find(\"label\");\n                                                    content.addClass(\"justificativa-tabela\");\n                                                    $(this).html(\"ver mais\");\n                                                }\n                                            })</script></div></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=editarEstimativaValor(estimativaValor) class=\"md-fab md-raised md-mini\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'alt-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=removerEstimativaValor(estimativaValor.cont)><md-icon ng-bind=\"\'delete\'\"><md-tooltip>{{\'alt-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginarEstimativas md-page-select></md-table-pagination></div></div></div></div><div class=\"m-t-30 m-b-30\" layout=row layout-xs=column flex=100 ng-show=\"laudoAvaliacao.tipoValorLaudo == ESTIMAR_VALORES\"><div flex=100 flex-sm=100 flex-xs=100><div layout=row>{{\'label-valor-estimado\' | translate}}:</div><div layout=row><label ng-bind=\"(valoresTemp.valor | currency).replace(\'R$\',\'\')\"></label></div></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=fecharEstimativasValor()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=gravarValoresCub(formularioDados);><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/cub/views/informarValorCubLaudoRelatorio.html","<form name=formularioDados><div layout=row layout-xs=column ng-if=\"tipoTerreno==TIPO_BENFEITORIA\"><div flex><h2 class=titulo-pagina ng-bind=\"(\'title-benfeitoria\' | translate) + \' \'\n                                + benfeitoria.codigo + \' - \'\n                                + benfeitoria.tipoBenfeitoria.descricao + \'/\'\n                                + (benfeitoria.edificacao ? benfeitoria.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></h2></div></div><div layout=row layout-align=\"start center\" layout-xs=column layout-align-xs=\"start start\" layout-sm=column layout-align-sm=\"start start\" layout-md=column layout-align-md=\"start start\" flex=100 ng-if=\"tipoTerreno==TIPO_BENFEITORIA\"><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-tipo\' | translate}}:</div><div layout=row><label ng-bind=benfeitoria.tipoBenfeitoria.descricao></label></div></div><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-descricao\' | translate}}:</div><div layout=row><label ng-bind=\"(benfeitoria.edificacao ? benfeitoria.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></div></div><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-area-construida\' | translate}}:</div><div layout=row><label ng-bind=\"(benfeitoria.areaConstruida | currency).replace(\'R$\',\'\')\"></label></div></div></div><!--###################### BENFEITORIAS ##########################--><!--###################### UNIDADEAUTONOMA ##########################--><div class=separarPaddingRows layout=row flex layout-align=\"start start\" ng-if=\"tipoTerreno==TIPO_UNIDADE_AUTONOMA\"><h2><label ng-bind=\"unidadeAutonomaImovel.edificacao.tipoEdificacao.descricao + \'/\'\n                                + unidadeAutonomaImovel.edificacao.tipoProjeto.descricao\"></label></h2></div><div layout=row layout-align=\"start center\" layout-xs=column layout-align-xs=\"start start\" layout-sm=column layout-align-sm=\"start start\" layout-md=column layout-align-md=\"start start\" flex=100 ng-if=\"tipoTerreno==TIPO_UNIDADE_AUTONOMA\"><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-tipo\' | translate}}:</div><div layout=row><label ng-bind=unidadeAutonomaImovel.edificacao.tipoEdificacao.descricao></label></div></div><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-descricao\' | translate}}:</div><div layout=row><label ng-bind=unidadeAutonomaImovel.edificacao.tipoProjeto.descricao></label></div></div><div flex=20 flex-sm=100 flex-xs=100><div layout=row>{{\'label-area-construida\' | translate}}:</div><div layout=row><label ng-bind=\"(unidadeAutonomaImovel.area | currency).replace(\'R$\',\'\')\"></label></div></div></div><!--###################### UNIDADEAUTONOMA ##########################--><div layout=row layout-xs=column class=m-t-20><md-input-container class=md-block flex=50 flex-sm=100 flex-xs=100><label>{{\'label-vida-util-ano\' | translate}}</label><input type=text maxlength=3 ui-number-mask=0 min=1 max=999 ng-model=valoresTemp.vidaUtil ng-change=calcularValorEstimado() name=\"{{nomeCampo}} - {{\'label-vida-util-ano\' | translate}}\" required></md-input-container><md-input-container class=md-block flex=50 flex-sm=100 flex-xs=100><label>{{\'label-idade-aparente-ano\' | translate}}</label><input type=text maxlength=3 ui-number-mask=0 min=0 max=999 ng-model=valoresTemp.idadeAparente ng-change=calcularValorEstimado() name=\"{{nomeCampo}} - Idade Aparente\" required></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-cub\' | translate}}</label><md-select ng-model=valoresTemp.cubSelecionado required ng-change=\"buscarValoresCubSiapa(valoresTemp.cubSelecionado, 0)\"><md-option ng-repeat=\"cub in tiposCub\" ng-value=cub>{{cub.nome}}</md-option></md-select></md-input-container><md-input-container class=\"md-block button-info-right\" flex=50 flex-xs=100 flex-sm=100><label>{{\'label-estado-conservacao\' | translate}}</label><md-select ng-model=valoresTemp.estadoConservacao required ng-change=calcularValorEstimado()><md-option ng-repeat=\"item in listaEstadosConservacao\" ng-value=item>{{item.nome}}</md-option></md-select><md-button ng-click=showDialogDepreciacaoHeidecke($event) style=\"position: absolute\"><md-icon flex=10 ng-bind=\"\'info\'\" class=\"md-fab md-raised md-mini\"><md-tooltip>Depreciação de Heidecke</md-tooltip></md-icon></md-button></md-input-container></div><div class=\"m-t-30 m-b-30\" layout=row layout-xs=column flex=100><div flex=100 flex-sm=100 flex-xs=100><div layout=row>{{\'label-valor-cub\' | translate}}: <span class=msg-erro-cub>{{msgErro}}</span></div><div layout=row><label ng-bind=\"(valoresTemp.valorCub | currency).replace(\'R$\',\'\')\"></label></div></div></div><div class=\"m-t-30 m-b-30\" layout=row layout-xs=column flex=100 ng-show=\"tipoValorLaudo == ESTIMAR_VALORES\"><div flex=100 flex-sm=100 flex-xs=100><div layout=row>{{\'label-valor-cub-depreciado\' | translate}}:</div><div layout=row><label ng-bind=\"(valoresTemp.valorCubDepreciado | currency).replace(\'R$\',\'\')\"></label></div></div></div><div ng-show=\"tipoValorLaudo == ESTIMAR_VALORES\"><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=valoresTemp.isFatoresAcrescimo ng-change=limparDadosFatoresAcrescimo(); name=\"{{\'label-inserir-fatores-acrescimo-cub\' | translate}}\"><label class=text-black>{{\'label-inserir-fatores-acrescimo-cub\' | translate}}</label></md-checkbox></div><div ng-show=valoresTemp.isFatoresAcrescimo><div layout=row layout-xs=column><h3 class=subtitulo-pagina>Fatores</h3></div><div layout=row layout-xs=column layout-sm=column flex=100><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-fatores-acrescimos\' | translate}}*</label><md-select ng-model=estimativa.motivo ng-change=\"estimativa.especificacao = null\"><md-option ng-repeat=\"(k,v) in listaMotivosEstimativa\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-input-container ng-show=\"estimativa.motivo == \'OUTRO\'\" class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-especificadao-do-fator\' | translate}}*</label><input name=\"{{\'label-especificadao-do-fator\' | translate}}\" ng-model=estimativa.especificacao></md-input-container><md-input-container class=md-block flex=\"estimativa.motivo == \'OUTRO\' ? \'33\' : \'66\" flex-xs=100 flex-sm=100><label>{{\'label-porcentagem\' | translate}}*</label><input name=\"{{\'label-porcentagem\' | translate}}\" ng-model=estimativa.porcentagem ui-percentage-mask=\"\"></md-input-container></div><div layout=row layout-xs=column flex=100><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-justificativa-fonte\' | translate}}*</label><textarea rows=1 cols=50 name=\"{{\'label-justificativa-fonte\' | translate}}\" ng-model=estimativa.justificativaFonte></textarea></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" ng-click=limparEstimativaValorCub()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button ng-show=!buttonEditarEstimativa class=\"md-primary md-raised m-l-15\" ng-click=adicionarEstimativaValorCub()><label>{{\'button-adicionar\' | translate}}</label></md-button><md-button ng-show=buttonEditarEstimativa class=\"md-primary md-raised m-l-15\" ng-click=adicionarEstimativaValorCub()><label>{{\'button-editar\' | translate}}</label></md-button></div></div></div><div layout=row layout-align=\"center start\" class=m-t-20 ng-show=\"paginacaoEstimativasValores.length > 0\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'tr-fator\' | translate}}</th><th md-column>{{\'tr-porcentagem\' | translate}}</th><th md-column>{{\'label-especificadao-do-fator\' | translate}}</th><th md-column>{{\'label-justificativa-fonte\' | translate}}</th><th md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"estimativaValor in paginacaoEstimativasValores | orderBy: estimativaValor.cont\"><td md-cell><label ng-bind=listaMotivosEstimativa[estimativaValor.motivo]></label></td><td md-cell><label ng-bind=\"(estimativaValor.porcentagem | percentage)\"></label></td><td md-cell><label class=descricao-tabela>{{estimativaValor.especificacao}}</label><a href=\"\" target=_blank class=btn-ver-mais ng-show=estimativaValor.especificacao>ver mais</a><div><script>$(\".btn-ver-mais\").unbind(\'click\');</script></div><div ng-if=\"paginacaoEstimativasValores.length - 1 == $index\"><script>$(\".btn-ver-mais\").on(\'click\', function () {\n                                                if ($(this).parent().find(\".descricao-tabela\").length > 0) {\n                                                    var content = $(this).parent().find(\".descricao-tabela\");\n                                                    console.log(content);\n                                                    content.removeClass(\"descricao-tabela\");\n                                                    $(this).html(\"ver menos\");\n                                                } else {\n                                                    var content = $(this).parent().find(\"label\");\n                                                    console.log(content);\n                                                    content.addClass(\"descricao-tabela\");\n                                                    $(this).html(\"ver mais\");\n                                                }\n                                            })</script></div></td><td md-cell><label class=justificativa-tabela>{{estimativaValor.justificativaFonte}}</label><a href=\"\" target=_blank class=btn-ver-mais-justificativa>ver mais</a><div><script>$(\".btn-ver-mais-justificativa\").unbind(\'click\');</script></div><div ng-if=\"paginacaoEstimativasValores.length - 1 == $index\"><script>$(\".btn-ver-mais-justificativa\").on(\'click\', function () {\n                                                if ($(this).parent().find(\".justificativa-tabela\").length > 0) {\n                                                    var content = $(this).parent().find(\".justificativa-tabela\");\n                                                    content.removeClass(\"justificativa-tabela\");\n                                                    $(this).html(\"ver menos\");\n                                                } else {\n                                                    var content = $(this).parent().find(\"label\");\n                                                    content.addClass(\"justificativa-tabela\");\n                                                    $(this).html(\"ver mais\");\n                                                }\n                                            })</script></div></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=editarEstimativaValor(estimativaValor) class=\"md-fab md-raised md-mini\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'alt-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=removerEstimativaValor(estimativaValor.cont)><md-icon ng-bind=\"\'delete\'\"><md-tooltip>{{\'alt-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginarEstimativas md-page-select></md-table-pagination></div></div></div></div><div class=\"m-t-30 m-b-30\" layout=row layout-xs=column flex=100 ng-show=\"tipoValorLaudo == ESTIMAR_VALORES\"><div flex=100 flex-sm=100 flex-xs=100><div layout=row>{{\'label-valor-estimado\' | translate}}:</div><div layout=row><label ng-bind=\"(valoresTemp.valor | currency).replace(\'R$\',\'\')\"></label></div></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=fecharEstimativasValor()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=gravarValoresCub(formularioDados);><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/dados-pessoas/language/pt-BR.json","{ }");
$templateCache.put("scripts-avaliacao/pages/dados-pessoas/views/dadosPessoas.html","<dados-pessoa responsavel=dadosPessoasCtrl.pessoa historico-pessoa-fisica=dadosPessoasCtrl.historicoPessoaFisica destinacao-por-responsavel=dadosPessoasCtrl.destinacaoPorResponsavel documento-pessoa-fisica=dadosPessoasCtrl.documentoPessoaFisica></dados-pessoa>");
$templateCache.put("scripts-avaliacao/pages/dados-pessoas/views/dadosPessoasJuridica.html","<dados-pessoa-juridica responsavel=dadosPessoasJuridicaCtrl.responsavel historico-pessoa-fisica=dadosPessoasJuridicaCtrl.historicoPessoaFisica></dados-pessoa-juridica>");
$templateCache.put("scripts-avaliacao/pages/dados-utilizacao/language/pt-BR.json","{ \"button-recusar\": \"Recusar\", \"button-fechar\": \"Fechar\", \"button-confirmar-selecao\": \"Confirmar Seleção\", \"button-adicionar\": \"Adicionar\", \"button-assinar\": \"Assinar\", \"button-cancelar\": \"Cancelar\", \"button-gravar-assinar-nota-tecnica\": \"Assinar Nota Técnica de Homologação e Gravar\", \"title-dados-utilizacoes-imovel\": \"Dados das Utilizações do Imóvel\", \"label-instrumento-destinacao\": \"Instrumento de Destinação\", \"label-codigo-contrato\": \"Código do Contrato\", \"label-tipo-uso\": \"Tipo de Uso\", \"label-especificacao\": \"Especificação\", \"label-descricao\": \"Descrição\", \"label-tipo-posse-ocupacao\": \"Tipo de Posse/Ocupação\", \"label-tipo-representacao\": \"Tipo de Representação\", \"label-ver-dados-pessoa-juridica\": \"Ver dados da pessoa jurídica\", \"label-cnpj\": \"CNPJ\", \"label-ug\": \"Código da UG\", \"label-nome-entidade\": \"Nome da entidade\", \"label-endereco-correspondencia\": \"Endereço de Correspondência\", \"label-responsavel-destinatario\": \"Responsável/Destinatário\", \"label-reponsavel-principal\": \"Responsável Principal\", \"label-cpf-cnpj\": \"CPF/CNPJ\", \"label-nome-razao-social\": \"Nome/Razão Social\", \"label-informacoes-entidade\": \"Informações da Entidade\", \"mensagem-responsavel-invalido\": \"CPF inválido\", \"mensagem-cnpj-invalido\": \"CNPJ inválido\" }");
$templateCache.put("scripts-avaliacao/pages/dados-utilizacao/views/apresentarDadosUtilizacao.html","<form name=formularioDadosUtilizacao novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-dados-utilizacoes-imovel\' | translate}}</h2></div></div><v-accordion multiple class=vAccordion--default style=margin-top:-20px><v-pane expanded=false ng-repeat=\"dados in dadosUtilizacoes\"><v-pane-header style=margin-bottom:20px class=textoAccordionPvgLaudo>{{\'Utilização - \' + dados.codigoUtilizacao}}</v-pane-header><v-pane-content><div layout=column flex><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-instrumento-destinacao\' | translate}}:</label><input type=text ng-model=dados.destinacao.tipoDestinacao.descricao ng-disabled=detalharDados></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label style=\"position: inherit; font-size: 10px\">{{\'label-codigo-contrato\' | translate}}:</label><input type=text ng-model=dados.destinacao.contrato.numero ng-disabled=detalharDados></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tipo-uso\' | translate}}:</label><input type=text ng-model=dados.destinacao.utilizacao.tipoUtilizacao.descricao ng-disabled=detalharDados></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-especificacao\' | translate}}:</label><input type=text ng-model=dados.destinacao.utilizacao.subTipoUtilizacao.descricao ng-disabled=detalharDados></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-descricao\' | translate}}:</label><input type=text ng-model=dados.destinacao.utilizacao.finalidade ng-required=true ng-disabled=detalharDados></md-input-container></div><div layout=row flex layout-align=\"start start\"><h3 class=subtitulo-pagina>{{\'label-responsavel-destinatario\' | translate}}</h3></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100><label>{{\'label-tipo-posse-ocupacao\' | translate}}:</label><md-select ng-model=dados.destinacao.dadosResponsavel.tipoPosseOcupacao name=\"{{nomeCampo}} - Ocupação\" ng-disabled=detalharDados><md-option ng-repeat=\"(k,v) in listaOcupacoes\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div layout=row flex layout-align=\"start start\"><h3 class=subtitulo-pagina>{{\'label-informacoes-entidade\' | translate}}</h3></div><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-tipo-representacao\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=dados.destinacao.dadosResponsavel.tipoRepresentacao ng-change=limpartipoNotaTecnicaHomologacao() name=\"{{nomeCampo}} - {{\'label-utilizar-laudo-preenchido\' | translate}}\" ng-disabled=detalharDados><md-radio-button ng-repeat=\"(k,v) in listaTiposRepresentacao\" flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary m-r-15\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></md-radio-group></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-cnpj\' | translate}}:</label><input type=text ng-model=dados.destinacao.dadosResponsavel.cnpj ng-disabled=detalharDados></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-ug\' | translate}}:</label><input type=text ng-model=dados.destinacao.dadosResponsavel.codigoUg ng-disabled=detalharDados></md-input-container></div><div style=\"margin-top: -20px; margin-bottom: 20px; z-index: 9999\" layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50><a href=\"\" class=mouse-pointer ng-click=detalharPessoaJuridica(dados.destinacao.dadosResponsavel.cnpj) target=_blank>{{\'label-ver-dados-pessoa-juridica\' | translate}}</a></div><div class=md-block flex-gt-xs flex=50></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-nome-entidade\' | translate}}:</label><input type=text ng-model=dados.destinacao.dadosResponsavel.nomeEntiade ng-disabled=detalharDados></md-input-container></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-endereco-correspondencia\' | translate}}:</span></span></div></div><div layout=row layout-xs=column class=m-b-20><md-container class=md-block flex=100 flex-sx=100><div><span ng-bind=\"dados.imovel.endereco.logradouro + \', N° \'\n                                                        + dados.imovel.endereco.numero + \', \'\n                                                        + dados.imovel.endereco.bairro + \', \'\n                                                        + dados.imovel.endereco.municipio + \'/\'\n                                                        + dados.imovel.endereco.uf + \' - \'\n                                                        + dados.imovel.endereco.pais + \'. CEP: \'\n                                                        + (dados.imovel.endereco.cep | cep)\"></span></div></md-container></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column width=10%>{{\'label-reponsavel-principal\' | translate}}</th><th md-column width=45%>{{\'label-cpf-cnpj\' | translate}}</th><th md-column width=45%>{{\'label-nome-razao-social\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"responsavel in dados.destinacao.dadosResponsavel.responsaveis\"><td md-cell><md-radio-group class=tamanho-radio-button-avaliacao ng-model=responsavel.isPrincipal ng-disabled=detalharDados><div layout=row><md-radio-button layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=20 ng-value=responsavel.isPrincipal></md-radio-button></div></md-radio-group></td><td md-cell ng-show=\"responsavel.cpfCnpj.length == 11\"><a href=\"\" ng-click=detalharPessoaFisica(responsavel.cpfCnpj) target=_blank>{{responsavel.cpfCnpj | cpfcnpj}}</a></td><td md-cell ng-show=\"responsavel.cpfCnpj.length == 14\"><a href=\"\" ng-click=detalharPessoaJuridica(responsavel.cpfCnpj) target=_blank>{{responsavel.cpfCnpj | cpfcnpj}}</a></td><td md-cell><label ng-bind=responsavel.nome></label></td></tr></tbody></table></md-table-container></div></div></div></v-pane-content></v-pane></v-accordion><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/documento-edificacao/views/cadastrarDocumentoEdificacao.html","<div class=separarPaddingRows layout=row flex layout-align=\"start start\"><label class=titulo-pagina>{{\'label-documento\' | translate}}</label></div><div><div layout=row layout-xs=column ng-show=!detalharLaudoRelatorio><div class=md-block flex-gt-xs flex=50></div><div class=md-block flex-gt-xs flex=50><label class=datepicker-label><label>{{\'label-possui-documento\' | translate}}*</label></label></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 ng-class=\"formEnviado && !documento.nome ? \'md-input-invalid\' : \'\'\"><label>{{\'label-nome-documento\' | translate}}*:</label><input ng-model=documento.nome type=text ng-disabled=\"detalharLaudoAvaliacao || documento.isPossui == \'SEM_INFORMACAO\'\"></md-input-container><md-radio-group flex=20 class=tamanho-radio-button-avaliacao ng-model=documento.isPossui ng-disabled=detalharLaudoAvaliacao name=\"{{nomeCampo}} - Possui este documento\"><div layout=row><md-radio-button ng-repeat=\"item in condicoes\" layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=item.valor><div class=texto-preto>{{item.nome}}</div></md-radio-button></div></md-radio-group></div><div layout=row layout-xs=column ng-show=!detalharLaudoRelatorio><div class=md-block flex-gt-xs flex=33><label class=datepicker-label><label>{{\'label-data-emissao\' | translate}}*</label></label></div><div class=md-block flex-gt-xs flex=33><label class=datepicker-label><label>{{\'label-data-validade\' | translate}}</label></label></div></div><div layout=row layout-xs=column><md-container class=md-block flex=33 layout=column flex-xs flex-sm layout-xs=row layout-sm=row ng-class=\"formEnviado && !documento.dtArquivo ? \'md-input-invalid\' : \'\'\"><md-datepicker ng-disabled=detalharLaudoAvaliacao ng-model=documento.dtEmissao md-placeholder=\"{{\'placeholder-data\' | translate}}*\" class=\"datePickerClass m-b-25\"></md-datepicker></md-container><md-container class=md-block flex=33 layout=column flex-xs flex-sm layout-xs=row layout-sm=row ng-class=\"formEnviado && !documento.dtArquivo ? \'md-input-invalid\' : \'\'\"><md-datepicker ng-disabled=detalharLaudoAvaliacao ng-model=documento.dtValidade md-max-date=filtroConsulta.dataFinalAssinatura md-placeholder=\"{{\'placeholder-data\' | translate}}*\" class=\"datePickerClass m-b-25\"></md-datepicker></md-container><div class=md-block flex-gt-xs flex=33 style=\"margin-top: -15px\"><md-button class=\"md-primary md-raised upload-button\" type=button ng-disabled=\"detalharLaudoAvaliacao || documento.arquivo.nome\" ngf-multiple=false ngf-select=adicionarArquivoDocumento($files)><label>{{\'label-selecionar\' | translate}}</label></md-button></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end></div></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=fecharInserirNovoDocumento()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button ng-show=!editar.buttonShow class=\"md-primary md-raised m-l-15\" type=button ng-click=adicionarListaDocumentos() ng-disabled=detalharLaudoAvaliacao><label>{{\'button-salvar\' | translate}}</label></md-button><md-button ng-show=editar.buttonShow class=\"md-primary md-raised m-l-15\" type=button ng-click=gravarAlteracaoListaDocumentos() ng-disabled=detalharLaudoAvaliacao><label>{{\'button-salvar\' | translate}}</label></md-button></div></div></div></div>");
$templateCache.put("scripts-avaliacao/pages/imovel-avaliacao/language/pt-BR.json","{ \"label-numero-rip\": \"RIP\", \"label-numero-rip-spiunet\": \"RIP Spiunet\", \"label-situacao-avaliacao\": \"Situação da Avaliação\", \"label-periodo-entre-data\": \"a\", \"label-tipo-do-imovel\":\"Tipo do Imóvel\", \"label-benfeitoria-do-imovel\": \"Benfeitoria do Imóvel\", \"label-classificacao-imovel\": \"Classificação do Imóvel\", \"label-cep\": \"CEP\", \"label-ordenar-resultados-por\": \"Ordenar resultados por\", \"label-periodo-cadastramento-imovel\": \"Período de Cadastramento do Imóvel\", \"label-ordenacao\": \"Ordenação\", \"label-dados-das-avaliacoes\": \"Dados das Avaliações\", \"label-editar-dados-das-avaliacoes\": \"Editar Dados das Avaliações\", \"label-visualizar-dados-das-avaliacoes\": \"Visualizar Dados das Avaliações\", \"label-informar-valores-do-laudo\": \"Informar valores do Laudo\", \"label-sim\": \"Sim\", \"label-nao\": \"Não\", \"select-avaliacoes-ausente-sem-dados\": \"Sem dados de Avaliação\", \"select-avaliacoes-ausente-sem-laudo\": \"Sem Laudo de Avaliação\", \"select-avaliacoes-ausente-sem-relatorio\": \"Sem Relatório de Valor de Referência\", \"radio-crescente\": \"Crescente\", \"radio-decrescente\": \"Decrescente\", \"tr-imovel-avaliado\": \"Imóvel Avaliado?\", \"campos-obrigatorios\": \"Formulário inválido. Campos obrigatórios não preenchidos.\" }");
$templateCache.put("scripts-avaliacao/pages/imovel-avaliacao/views/consultarDadosAvaliacao.html","<form name=formularioConsultaImovel><md-content><div id=formData layout=column><toaster-container></toaster-container><div layout=row flex><h2>{{\'label-consulta-de-imoveis\' | translate}}</h2></div><div layout=row layout-xs=column><div flex-xs=100 flex-sm=100 flex=66></div><div class=md-block flex-gt-xs flex=33 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-periodo-cadastramento-imovel\' | translate}}:</span></span></div></div><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=33 class=md-block layout-xs=row layout-sm=row><label>{{\'label-numero-rip\' | translate}}:</label><input ng-model=filtroConsulta.rip maxlength=8 name=\"{{nomeCampo}} - {{\'label-numero-rip\' | translate}}\" ng-change=verificarObrigatoriedadePais()></md-input-container><md-input-container class=md-block flex=33 flex-xs layout-xs=row layout-sm=row><label>{{\'label-situacao-avaliacao\' | translate}}:</label><md-select ng-model=filtroConsulta.situacaoAvaliacao name=\"{{nomeCampo}} - {{\'label-situacao-avaliacao\' | translate}}\" placeholder=\"{{\'label-situacao-avaliacao\' | translate}}\"><md-option ng-repeat=\"(k,v) in avaliacoesAusentes\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-container flex=33 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row layout-xs=row><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=filtroConsulta.dataInicial md-max-date=hoje md-placeholder=\"{{\'placeholder-data-inicial\' | translate}}\"></md-datepicker></div><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=filtroConsulta.dataFinal md-max-date=hoje md-placeholder=\"{{\'placeholder-data-final\' | translate}}\"></md-datepicker></div></div></md-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container layout-xs=row flex=50 flex-xs=100 flex-sm=100 class=md-block layout-xs=row layout-sm=row><label>{{\'label-numero-rip-spiunet\' | translate}}:</label><input ng-model=filtroConsulta.ripSpiunet maxlength=13 name=\"{{nomeCampo}} - {{\'label-numero-rip-spiunet\' | translate}}\" ng-change=verificarObrigatoriedadePais()></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tipo-do-imovel\' | translate}}:</label><md-select ng-model=filtroConsulta.idTipoImovel name=\"{{nomeCampo}} - {{\'label-tipo-do-imovel\' | translate}}\" placeholder=\"{{\'label-tipo-do-imovel\' | translate}}\"><md-option ng-repeat=\"tipoImovel in listaTiposImovel\" ng-value=tipoImovel.id>{{tipoImovel.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex=100 flex-sm=100 flex-xs=100><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-pais\' | translate}}:</label><md-select ng-model=filtroConsulta.idPais name=\"{{nomeCampo}} - {{\'label-pais\' | translate}}\" placeholder=\"{{\'label-pais\' | translate}}\" ng-required=requiredPais ng-change=verificarObrigatoriedadeEstado()><md-option ng-repeat=\"pais in listaPaises\" ng-value=pais.id>{{pais.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 ng-show=selectedEstado><label>{{\'label-uf\' | translate}}:</label><md-select ng-model=filtroConsulta.uf ng-change=pesquisarMunicipiosPorUf() name=\"{{nomeCampo}} - {{\'label-uf\' | translate}}\" placeholder=\"{{\'label-uf\' | translate}}\" ng-required=requiredEstado><md-option ng-repeat=\"uf in listaUFs\" ng-value=uf.sigla>{{uf.descricao}}</md-option></md-select></md-input-container><md-input-container layout-xs=row flex=33 class=md-block layout-xs=row layout-sm=row ng-if=\"!filtroConsulta.idPais || filtroConsulta.idPais == idBrasil\"><label class=concerta-label>{{\'label-cep\' | translate}}:</label><input type=text ng-model=filtroConsulta.cep ui-br-cep-mask name=\"{{nomeCampo}} - {{\'label-cep\' | translate}}\" ng-change=verificarObrigatoriedadePais()></md-input-container></div><div layout=row layout-xs=column flex=100 flex-sm=100 flex-xs=100><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100 ng-if=\"!filtroConsulta.idPais || filtroConsulta.idPais == idBrasil\"><label>{{\'label-municipio\' | translate}}:</label><md-select ng-model=filtroConsulta.municipio name=\"{{nomeCampo}} - {{\'label-municipio\' | translate}}\" placeholder=\"{{\'label-municipio\' | translate}}\"><md-option ng-repeat=\"municipio in listaMunicipios\" ng-value=municipio.nome>{{municipio.nome}}</md-option></md-select></md-input-container><!-- Exibição do campo \'Cidade do Exterior\' ao selecionar país diferente Brasil.\n                Serviço de cidades estrangeiras ainda não implementado no backend. Campo desabilitado até definição da regra de preenchimento.\n                <md-input-container class=\"md-block\" flex=\"100\" flex-xs=\"100\" flex-sm=\"100\" ng-if=\"filtroConsulta.idPais && filtroConsulta.idPais != idBrasil\">\n                    <label>{{\'label-cidade-exterior\' | translate}}:</label>\n                    <md-select ng-model=\"filtroConsulta.municipio\"\n                               name=\"{{nomeCampo}} - {{\'label-cidade-exterior\' | translate}}\"\n                               placeholder=\"{{\'label-cidade-exterior\' | translate}}\">\n                        <md-option ng-repeat=\"cidadeExterior in listaCidadesExterior\" ng-value=\"cidadeExterior.nome\">\n                            {{cidadeExterior.nome}}\n                        </md-option>\n                    </md-select>\n                </md-input-container>\n                --></div><!-- Uma vez que os campos \'País\' e \'UF\' passaram a ser obrigatórios, a ordenação de campos perdeu a finalidade.\n            <div layout=\"row\" layout-xs=\"column\">\n                <div flex-xs=\"100\" flex-sm=\"100\" flex=\"33\"></div>\n                <div class=\"md-block\" flex-gt-xs flex=\"33\" style=\"margin-top: -15px\">\n                    <span class=\"datepicker-label\">\n                        <span>{{\'label-ordenacao\' | translate}}:</span>\n                    </span>\n                </div>\n            </div>\n\n            <div layout=\"row\" layout-xs=\"column\" flex=\"100\" flex-sm=\"column\" flex-sm=\"100\" flex-xs=\"100\">\n                <md-input-container class=\"md-block\" flex=\"33\" flex-xs=\"100\" flex-sm=\"100\">\n                    <label>{{\'label-ordenar-resultados-por\' | translate}}:</label>\n                    <md-select ng-model=\"filtroConsulta.campoOrdenacao\"\n                               name=\"{{nomeCampo}} - {{\'label-ordenar-resultados-por\' | translate}}\"\n                               placeholder=\"{{\'label-ordenar-resultados-por\' | translate}}\" required>\n                        <md-option ng-repeat=\"(k,v) in ordenacaoCampos\" ng-value=\"k\">\n                            {{v}}\n                        </md-option>\n                    </md-select>\n                </md-input-container>\n\n                <md-input class=\"md-block\" flex=\"33\" flex-xs=\"100\" flex-sm=\"100\" layout=\"column\">\n                    <md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=\"filtroConsulta.ordem\"\n                                    name=\"{{nomeCampo}} - {{\'label-ordenacao\' | translate}}\">\n                        <div layout=\"row\">\n                            <md-radio-button flex=\"30\" flex-xs=\"100\" flex-sm=\"100\" flex-md=\"100\" class=\"tamanho-radio-button-avaliacao md-primary\"\n                                             ng-value=\"CRESCENTE\">\n                                <div class=\"texto-preto\">{{\'radio-crescente\' | translate}}</div>\n                            </md-radio-button>\n                            <md-radio-button flex=\"30\" flex-xs=\"100\" flex-sm=\"100\" flex-md=\"100\" class=\"tamanho-radio-button-avaliacao md-primary\"\n                                             ng-value=\"DECRESCENTE\">\n                                <div class=\"texto-preto\">{{\'radio-decrescente\' | translate}}</div>\n                            </md-radio-button>\n                        </div>\n                    </md-radio-group>\n                </md-input>\n                <div flex=\"30\"></div>\n            </div> --><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=limparCamposPesquisa()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=pesquisarDadosDetalharImovelAvaliacao()><label>{{\'button-pesquisar\' | translate}}</label></md-button></div></div></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column width=10%>{{\'tr-rip\' | translate}}</th><th md-column width=10%>{{\'rip-spiunet\' | translate}}</th><th md-column width=20%>{{\'tr-proprietario-titular\' | translate}}</th><th md-column width=40%>{{\'tr-endereco\' | translate}}</th><th md-column width=10%>{{\'tr-imovel-avaliado\' | translate}}</th><th md-column width=5%>{{\'tr-acao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"dadosImovelAvaliacao in listaDadosImoveisAvaliacao\"><td md-cell><span ng-bind=dadosImovelAvaliacao.rip></span></td><td md-cell><span ng-bind=\"dadosImovelAvaliacao.dsSpiunet | ripSpiuNet\"></span></td><td md-cell><span ng-bind=dadosImovelAvaliacao.proprietario.tipoProprietarioReal.descricao></span></td><td md-cell><span ng-bind=formatarEnderecoCompleto(dadosImovelAvaliacao.enderecamento)></span></td><td md-cell><a ng-show=dadosImovelAvaliacao.avaliado ng-click=\"detalheAvaliacao($event, dadosImovelAvaliacao)\" href=\"\"><label ng-show=dadosImovelAvaliacao.avaliado>{{\'label-sim\' | translate}}</label></a><label ng-show=!dadosImovelAvaliacao.avaliado>{{\'label-nao\' | translate}}</label></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=\"abrirDialogMapaDetalharImovel($event, dadosImovelAvaliacao.pontoImovel, dadosImovelAvaliacao)\" aria-label=\"Ver Localização\"><md-icon ng-bind=\"\'map\'\"><md-tooltip>{{\'label-ver-localizacao\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(dadosImovelAvaliacao.enderecamento.uf) != -1)\" class=\"md-fab md-raised md-mini\" ng-click=redirecionarDetalharImovelAvaliacoes(dadosImovelAvaliacao.rip) aria-label=\"Dados Das Avaliações\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'label-editar-dados-das-avaliacoes\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"(usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'false\' && usuarioLogado.AVALIACAO_CONSULTAR_VISUALIZAR_DADOS_AVALIACAO == \'true\') || (usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && usuarioLogado.QUALQUER_JURISDICAO == \'false\' && usuarioLogado.JURISDICOES.indexOf(dadosImovelAvaliacao.enderecamento.uf) == -1)\" class=\"md-fab md-raised md-mini\" ng-click=redirecionarDetalharImovelAvaliacoes(dadosImovelAvaliacao.rip) aria-label=\"Dados Das Avaliações\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>{{\'label-visualizar-dados-das-avaliacoes\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarDadosDetalharImovelAvaliacao md-page-select></md-table-pagination></div></div><div layout=row layout-xs=column class=buttons-action ng-show=\"listaDadosImoveisAvaliacao.length > 0\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=\"abrirDialogMapaDetalharLocalizacoesImoveis($event, listaDadosImoveisAvaliacao)\"><label>{{\'button-visualizar-no-mapa\' | translate}}</label></md-button></div></div></div></div></md-content></form>");
$templateCache.put("scripts-avaliacao/pages/imovel-laudo-relatorio/views/consultarImovelLaudo.html","<form name=consultarImovelLaudo novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-consultar-imovel\' | translate}}</h2></div></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=35 flex-xs=100 flex-sm=100><label>{{\'label-pais\' | translate}}</label><md-select ng-model=filtroConsulta.idPais name=\"{{\'label-pais\' | translate}}\" placeholder=\"{{\'label-pais\' | translate}}\"><md-option ng-repeat=\"pais in listaPaises\" ng-value=pais.id>{{pais.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=30 flex-xs=100 flex-sm=100 flex-offset=5 ng-show=\"!filtroConsulta.idPais || filtroConsulta.idPais == idBrasil\"><label>{{\'label-cep\' | translate}}</label><input ng-model=filtroConsulta.cep name=\"{{\'label-cep\' | translate}}\" ng-change=consultarLocalidadeEnderecoCep(); ui-br-cep-mask></md-input-container><md-input-container class=md-block flex=35 flex-xs=100 flex-sm=100 flex-offset=5 ng-show=\"!filtroConsulta.idPais || filtroConsulta.idPais == idBrasil\"><label>{{\'label-uf\' | translate}}</label><md-select ng-model=filtroConsulta.uf name=\"{{\'label-uf\' | translate}}\" ng-disabled=cepInformado ng-change=pesquisarMunicipiosPorUf() placeholder=\"{{\'label-uf\' | translate}}\"><md-option ng-repeat=\"uf in listaUFs\" ng-value=uf.sigla ng-selected=\"uf.sigla == filtroConsulta.idPais\">{{uf.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=65 flex-xs=100 flex-sm=100 flex-offset=5 ng-show=\"filtroConsulta.idPais && filtroConsulta.idPais != idBrasil\"><label>{{\'label-cidade-exterior\' | translate}}</label><md-select ng-model=filtroConsulta.cidadeExterior name=\"{{\'label-cidade-exterior\' | translate}}\" placeholder=\"{{\'label-cidade-exterior\' | translate}}\"><md-option></md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex-sm=column ng-show=\"!filtroConsulta.idPais || filtroConsulta.idPais == idBrasil\"><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-municipio\' | translate}}</label><md-select ng-model=filtroConsulta.municipio name=\"{{nomeCampo}} - {{\'label-municipio\' | translate}}\" placeholder=\"{{\'label-municipio\' | translate}}\" ng-disabled=\"cepInformado && !cepNaoGenerico\"><md-option ng-repeat=\"municipio in listaMunicipios | orderBy: \'nome\'\" ng-value=municipio.nome ng-selected=\"municipio.nome == filtroConsulta.municipio\">{{municipio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tipo-logradouro\' | translate}}</label><md-select ng-model=filtroConsulta.tipoLogradouro name=\"{{\'label-tipo-logradouro\' | translate}}\" placeholder=\"{{\'label-tipo-logradouro\' | translate}}\" ng-disabled=\"cepInformado && !cepNaoGenerico\"><md-option ng-repeat=\"tipoLogradouro in listaTiposLogradouro\" ng-value=tipoLogradouro.descricao>{{tipoLogradouro.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex-sm=column ng-if=\"!filtroConsulta.idPais || filtroConsulta.idPais == idBrasil\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-logradouro\' | translate}}</label><input ng-model=filtroConsulta.logradouro name=\"{{\'label-logradouro\' | translate}}\" ng-disabled=\"cepInformado && !cepNaoGenerico\"></md-input-container></div><div layout=row layout-xs=column flex-sm=column ng-if=\"!filtroConsulta.idPais || filtroConsulta.idPais == idBrasil\"><md-input-container class=md-block flex=30 flex-xs=100 flex-sm=100><label>{{\'label-numero\' | translate}}</label><input ng-model=filtroConsulta.numero name=\"{{\'label-numero\' | translate}}\"></md-input-container><md-input-container class=md-block flex=35 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-bairro-distrito\' | translate}}</label><input ng-model=filtroConsulta.bairro name=\"{{\'label-bairro-distrito\' | translate}}\" ng-disabled=\"cepInformado && !cepNaoGenerico\"></md-input-container><md-input-container class=md-block flex=35 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-complemento\' | translate}}</label><input ng-model=filtroConsulta.complemento name=\"{{\'label-complemento\' | translate}}\"></md-input-container></div><div layout=row layout-xs=column flex-sm=column ng-if=\"filtroConsulta.idPais && filtroConsulta.idPais != idBrasil\"><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-codigo-postal\' | translate}}</label><input ng-model=filtroConsulta.codigoPostal name=\"{{\'label-codigo-postal\' | translate}}\"></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-endereco\' | translate}}</label><input ng-model=filtroConsulta.endereco name=\"{{\'label-endereco\' | translate}}\"></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=voltarCadastrarLaudoRelatorio();><label>{{\'button-voltar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=limparCamposPesquisa();><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=buscarImoveisLaudoRelatorio();><label>{{\'button-consultar\' | translate}}</label></md-button></div></div></div><div flex><div layout=column><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=ripSelecionado><div layout=row layout-align=\"center start\" ng-show=\"listaDadosImoveisLaudoRelatorio.length > 0\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th flex=10 md-column>{{\'tr-rip\' | translate}}</th><th flex=30 md-column>{{\'tr-endereco\' | translate}}</th><th flex=20 md-column>{{\'tr-matricula-transcricao\' | translate}}</th><th flex=20 md-column>{{\'tr-proprietario\' | translate}}</th><th flex=10 md-column>{{\'tr-selecionar\' | translate}}</th><th flex=10 md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"dadoImovelLaudoRelatorio in listaDadosImoveisLaudoRelatorio\"><td md-cell><label ng-bind=dadoImovelLaudoRelatorio.rip></label></td><td md-cell ng-show=\"dadoImovelLaudoRelatorio.enderecamento.pais == BRASIL\"><span ng-bind=\"dadoImovelLaudoRelatorio.enderecamento.logradouro + \', Número \'\n                                                        + dadoImovelLaudoRelatorio.enderecamento.numero + \', \'\n                                                        + dadoImovelLaudoRelatorio.enderecamento.bairro + \', \'\n                                                        + dadoImovelLaudoRelatorio.enderecamento.municipio + \'/\'\n                                                        + dadoImovelLaudoRelatorio.enderecamento.uf + \' - \'\n                                                        + dadoImovelLaudoRelatorio.enderecamento.pais + \'. CEP: \'\n                                                        + (dadoImovelLaudoRelatorio.enderecamento.cep | cep)\"></span></td><td md-cell ng-show=\"dadoImovelLaudoRelatorio.enderecamento.pais != BRASIL\"><span ng-bind=\"dadoImovelLaudoRelatorio.enderecamento.endereco + \', \'\n                                                        + dadoImovelLaudoRelatorio.enderecamento.cidadeExterior + \' - \'\n                                                        + dadoImovelLaudoRelatorio.enderecamento.pais + \'. Código Postal: \'\n                                                        + dadoImovelLaudoRelatorio.enderecamento.codigoPostal\"></span></td><td md-cell><label ng-bind=dadoImovelLaudoRelatorio.matricula></label></td><td md-cell><span ng-bind=dadoImovelLaudoRelatorio.proprietario.tipoProprietarioReal.descricao></span></td><td md-cell><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" layout-align=\"center center\" ng-value=dadoImovelLaudoRelatorio.rip></md-radio-button></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=\"abrirDialogImagensImovel($event, dadoImovelLaudoRelatorio.rip)\"><md-icon ng-bind=\"\'photo_library\'\"><md-tooltip>{{\'title-fotos-do-imovel\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"abrirDialogMapaDetalharImovel($event, dadoImovelLaudoRelatorio.pontoImovel, dadoImovelLaudoRelatorio)\"><md-icon ng-bind=\"\'location_on\'\"><md-tooltip>{{\'label-ver-localizacao\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=buscarImoveisLaudoRelatorio md-page-select></md-table-pagination></div></div></md-radio-group></div></div><div layout=row layout-xs=column class=buttons-action ng-show=\"listaDadosImoveisLaudoRelatorio.length > 0\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=confimarImovelSelecionado();><label>{{\'button-confirmar-selecao-imovel\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/imovel-laudo-relatorio/language/pt-BR.json","{ \"label-solicitante\": \"Solicitante\", \"label-objetivo\": \"Objetivo\", \"label-finalidade\": \"Finalidade\", \"label-tipo\": \"Tipo\", \"label-tratamento\":\"Tratamento\", \"label-ferramenta-analitica\":\"Ferramenta Analítica\", \"label-normas-utilizadas\":\"Normas Utilizadas\", \"label-nao-sei-rip\": \"Não sei o RIP\", \"label-tipo-logradouro\" : \"Tipo Logradouro\", \"label-numero\":\"Nº\", \"label-bairro-distrito\":\"Bairro/Distrito\", \"label-complemento\":\"Complemento\", \"label-codigo-postal\":\"Código Postal\", \"label-endereco\":\"Endereço\", \"title-consultar-imovel\": \"Consultar Imóvel\", \"button-confirmar-selecao-imovel\" : \"Confirmar Seleção do Imóvel\" }");
$templateCache.put("scripts-avaliacao/pages/laudo-avaliacao/language/pt-BR.json","{ \"button-recusar\": \"Recusar\", \"button-fechar\": \"Fechar\", \"button-confirmar-selecao\": \"Confirmar Seleção\", \"button-adicionar\": \"Adicionar\", \"button-assinar\": \"Assinar\", \"button-cancelar\": \"Cancelar\", \"button-gravar-assinar-nota-tecnica\": \"Assinar Nota Técnica de Homologação e Gravar\", \"label-cpf-homologador\": \"CPF do Homologador\", \"label-cpf-homologador-se-laudo-contratado\": \"CPF do Homologador (se laudo contratado)\", \"label-cpf-responsavel-tecnico\": \"CPF do Responsável Técnico\", \"label-data-de-assinatura\": \"Data da assinatura\", \"label-moeda\": \"Moeda\", \"label-nome-homologador\": \"Nome do Homologador\", \"label-nome-responsavel-tecnico\": \"Nome do Responsável Tecnico\", \"label-numero-crea-responsavel\": \"Nº do CREA/CAU do Responsável Técnico\", \"label-tipo-avaliacao\": \"Tipo de avaliação\", \"label-prazo-validade\": \"Prazo de Validade\", \"label-valor-imovel\": \"Valor do Imóvel\", \"label-valor-locacao-imovel\": \"Valor de Locação do Imóvel\", \"label-utilizar-laudo-preenchido\": \"Utilizar Laudo/Relatório preenchido no sistema?\", \"label-preenchida-em\": \"Preenchida em\", \"label-preenchimento-incompleto\": \"Preenchimento incompleto\", \"label-sim\": \"Sim\", \"label-nao\": \"Não\", \"label-responsavel-tecnico\": \"Responsável Técnico\", \"label-cpf\": \"CPF\", \"label-nome\": \"Nome\", \"label-crea-cau\": \"CREA/CAU\", \"label-valor-terreno-benfeitoria\": \"Valores de Terreno e Benfeitoria\", \"label-laudo-relatorio-elaborado\": \"Laudo/Relatório Elaborado\", \"label-laudo-elaborado\": \"Laudo Elaborado\", \"label-adicionar\": \"Adicionar\", \"label-assinatura-nota-tecnica\": \"Assinatura da Nota Técnica\", \"label-acoes\": \"Ações\", \"label-editar\": \"Editar\", \"label-excluir\": \"Excluir\", \"label-nota-tecnica-homologacao\": \"Nota Técnica de Homologação\", \"label-upload-arquivo\": \"Upload de Arquivo (PDF)\", \"label-preencher-sistema\": \"Preencher no Sistema\", \"label-preencher-alterar-nota\": \"Preencher/Alterar Nota Técnica\", \"label-situacao\": \"Situação\", \"label-laudo-avaliacao-relatorio\": \"Laudo de Avaliação/Relatório de Referência\", \"label-laudo-avaliacao\": \"Laudo de Avaliação\", \"label-relatorio-valor-referencia\": \"Relatório de Valor de Referência\", \"label-sumario-executivo\": \"Sumário Executivo\", \"label-analise-laudo\": \"Análise do Laudo\", \"label-id\": \"ID\", \"label-tipo\": \"Tipo\", \"label-descricao\": \"Descrição\", \"label-area-construida\": \"Área Construída\", \"label-vida-util\": \"Vida Útil (Ano)\", \"label-cub\": \"CUB\", \"label-cub-depreciado\": \"CUB Depreciado\", \"label-idade-aparente\": \"Idade Aparente (Ano)\", \"label-valor-estimado\": \"Valor Estimado\", \"label-valor-estimado-total\": \"VALOR ESTIMADO TOTAL\", \"label-fonte-valor-terreno\": \"Fonte do valor do terreno\", \"label-pvg-spu\": \"PVG/SPU\", \"label-valor-venal\": \"Valor Venal/Município\", \"label-referencia-incra\": \"Referência INCRA\", \"label-outra-fonte\": \"Outra Fonte\", \"label-valor-terreno-rs\": \"Valor do Terreno (R$)\", \"label-valor-m2\": \"Valor do m² (R$)\", \"label-periodo-avaliacao\": \"Período da Avaliação\", \"label-valor-terreno\": \"Valor do Terreno\", \"label-fonte\": \"Fonte\", \"label-numero\": \"Número\", \"label-metodo\": \"Método\", \"label-data-assinatura\": \"Data da Assinatura\", \"label-data\": \"Data\", \"label-valor\": \"Valor\", \"label-responsavel\": \"Responsável\", \"label-confirmar-selecao\": \"Confirmar Seleção\", \"label-verificacao-itens-obrigatorios-intrucao-normativa\": \"Verificação de Itens Obritatórios Segundo a Instrução Normativa\", \"label-aspectos\": \"Aspectos\", \"label-verificacao\": \"Verificação\", \"label-observacao-justificativa\": \"Observação/Justificativa\", \"label-atende\": \"Atende\", \"label-nao-atende\": \"Não Atende\", \"label-aspectos-I\": \"I - a identificação da pessoa física ou jurídica e/ou seu representante legal que solicitou o serviço;\", \"label-aspectos-II\": \"II - o objetivo e finalidade da avaliação;\", \"label-aspectos-III\": \"III - a identificação e caracterização do bem avaliando;\", \"label-aspectos-IV\": \"IV - a verificação da metodologia utilizada e sua justificativa;\", \"label-aspectos-V\": \"V - a especificação da avaliação quanto à indicação dos graus de fundamentação e precisão atingidos, confirmando-os quando couber, com a exibição das tabelas previstas da NBR 14.653, devidamente pontuadas, em consonância com a metodologia adotada no laudo\", \"label-aspectos-VI\": \"VI - a qualificação legal completa e assinatura do(s) profissional(is) responsável(is) pela avaliação;\", \"label-aspectos-VII\": \"VII - a conferência da validade da avaliação em observância às orientações vigentes da SPU, dentre elas:\", \"label-aspectos-VIII\": \"VIII - a verificação quanto ao recolhimento da ART, quando for o caso;\", \"label-aspectos-IX\": \"IX - a verificação quanto ao tratamento dos dados e identificação do resultado, explicitação do campo de arbítrio e intervalos de confiança, se for o caso, e justificativas para o resultado adotado;\", \"label-aspectos-X\": \"X - a confirmação do valor determinado na equação apresentada.\", \"label-aspectos-VII-a\": \"a) apresentação do gráfico \\\"preços observados versus valores estimados pelo modelo\\\", conforme item 8.2.1.4.1 da NBR 14.653-2;\", \"label-aspectos-VII-b\": \"b) quando da utilização do Tratamento de Dados por Fatores, deverá apresentar a sua validação (publicações de entidades técnicas reconhecidas ou deduzidos ou referendados pelo próprio avaliador), conforme item 8.2.1.4.2 da NBR 14.653-2;\", \"label-aspectos-VII-c\": \"c) em caso de existência de \\\"outliers\\\", deverá apresentar análise de aceitação;\", \"label-aspectos-VII-d\": \"d) se o coeficiente de correlação for inferior a 0.70 para os laudos de avaliação elaborados com tratamento científico de dados de mercado, deverá ser justificado;\", \"label-aspectos-VII-e\": \"e) caso a correlação entre as variáveis independentes seja superior a 0,84, deverá apresentar justificativas;\", \"label-consideracoes-finais\": \"Considerações Finais\", \"label-assinaturas\": \"Assinaturas\", \"label-responsaveis-nota-tecnica-homologacao\": \"Responsáveis pela nota técnica de homologação\", \"label-documento-assinado-em\":\"Documento assinado eletônicamente em\", \"label-documento-assinado\": \"Documento assinado eletronicamente\", \"label-revalidacao-laudo-avaliacao\": \"Revalidação do Laudo de Avaliação\", \"label-analise-mercado\": \"Análise de Mercado\", \"label-termo-responsabilidade\": \"Termo de Responsabilidade\", \"label-em\": \"em\", \"label-as\": \"às\", \"label-texto-info-verdadeira\": \"Certifico que as informações prestadas são verdadeiras e que o arquivo do Laudo/Relatório de Avaliação está correto.\", \"label-orgao\": \"Órgão\", \"label-unidade\": \"Unidade\", \"label-subunidade\": \"Subunidade\", \"label-motivo-cancelamento\": \"Motivo\", \"label-justificativa\": \"Justificativa\", \"label-nome-arquivo\": \"Nome Arquivo\", \"label-arquivo\": \"Arquivo\", \"label-tipo-metodologia\": \"Metodologia\", \"label-tipo-metodo-avaliacao\": \"Método de avaliação\", \"label-parecer-tecnico\": \"Parecer Técnico\", \"label-descricao-outra-metodologia\": \"Descrição da Outra Metodologia\", \"label-nao-necessita-homologacao\": \"Não necessita de homologação\", \"label-justificativa-nao-homologacao\": \"Justificativa para a não homologação do laudo\", \"label-utilizar-documento-sei\": \"Utilizar Documento SEI\", \"label-enviar-documento-para-sei\": \"Enviar documento para o SEI\", \"label-criar-novo-processo-sei\": \"Criar novo processo no SEI\", \"label-utilizar-processo-existente-sei\": \"Utilizar processo existente no SEI\", \"label-numero-processo-sei\": \"Número do Processo SEI\", \"label-n-processo\": \"N° do Processo\", \"label-rip-spiunet\": \"RIP Spiunet\", \"label-rip\": \"RIP\", \"placeholder-data-assinatura\": \"Data da assinatura\", \"placeholder-data\": \"Data\", \"title-inserir-dados-laudo-relatorio-avaliacao\": \"Inserir Dados do Laudo/Relatório de Avaliação\", \"title-inserir-dados-laudo-avaliacao\": \"Inserir Dados do Laudo de Avaliação\", \"title-inserir-dados-laudo-avaliacao-locacao\": \"Inserir Dados do Laudo de Avaliação - Locação/Arrendamento\", \"title-inserir-dados-relatorio\": \"Inserir Dados do Relatório de Valor de Referência\", \"title-dados-laudo-relatorio-avaliacao\": \"Dados do Laudo/Relatório de Avaliação\", \"title-dados-cancelamento\": \"Dados do cancelamento\", \"title-dados-revalidacao\": \"Dados da Revalidação\", \"title-cancelar-laudo-avaliacao\": \"Cancelar Laudo de Avaliação\", \"title-cancelar-laudo-avaliacao-locacao\": \"Cancelar Laudo de Avaliação - Locação/Arrendamento\", \"title-cancelar-relatorio-valor-referencia\": \"Cancelar Relatório de Valor de Referência\", \"title-dados-laudo-avaliacao\": \"Dados do Laudo de Avaliação\", \"title-detalhar-laudo-avaliacao\": \"Detalhar Laudo de Avaliação\", \"title-detalhar-laudo-avaliacao-locacao\": \"Detalhar Laudo de Avaliação - Locação/Arrendamento\", \"title-detalhar-relatorio-valor-referencia\": \"Detalhar Relatório de Valor de Referência\", \"title-detalhar-relatorio\": \"Detalhar Laudo do Relatório de Valor de Referência\", \"title-dados-relatorio\": \"Dados do Relatório\", \"title-dados-laudo\": \"Dados do Laudo\", \"title-arquivos-laudo\":\"Arquivos laudo revalidação\", \"title-arquivos\":\"Arquivos\", \"title-data-revalidacao\": \"Data da revalidação:\", \"title-periodo-validade\":\"Periodo de validade após a validação:\", \"title-nota-revalidacao\":\"Nota de Revalidação\", \"title-anexos\":\"Anexos\", \"title-pendencia-validacao\":\"Pendente de Validação\", \"mensagem-termo-responsabilidade-laudo-avaliacao\": \"certifico que as informações prestadas são verdadeiras e que o arquivo do Laudo de Avaliação está correto. O valor atribuído ao imóvel é de inteira responsabilidade do avaliador responsável pela avaliação.\", \"mensagem-termo-responsabilidade-laudo-avaliacao-locacao\": \"certifico que as informações prestadas são verdadeiras e que o arquivo do Laudo de Avaliação para Locação/Arrendamento está correto. O valor atribuído ao imóvel é de inteira responsabilidade do avaliador responsável pela avaliação.\", \"mensagem-termo-responsabilidade-relatorio-valor-referencia\": \"certifico que as informações prestadas são verdadeiras e que o arquivo do Relatório de Valor de Referência está correto. O valor atribuído ao imóvel é de inteira responsabilidade do avaliador responsável pela avaliação.\", \"mensagem-valor-imovel-diferente-valor-terreno\": \"O valor da área construída e do terreno não corresponde ao valor do imóvel.\", \"mensagem-data-assinatura-posterior-data-atual\": \"A data da assinatura não pode ser posterior a data atual.\", \"mensagem-data-assinatura-outro-laudo\": \"A data da assinatura não pode ser igual a de outro laudo já cadastrado.\", \"mensagem-are-construida-bao-corresponde-valor-imovel\": \"O valor da área construída e do terreno não corresponde ao valor do imóvel\", \"mensagem-mesmo-responsavel-homologador\": \"O mesmo CPF não pode pertencer à um responsável técnico e homologador.\", \"mensagem-validar-valor-imovel\": \"O valor do terreno ou das edificações é superior ao valor do imóvel informado ou adotado.\", \"mensagem-validar-valor-imovel-diferente\": \"O valor do terreno com as edificações estavam diferentes do valor do imóvel informado ou adotado.\", \"mensagem-preenchimento-incompleto-nota-tenica\": \"Preenchimento incompleto da nota técnica de homologação.\", \"mensagem-adicionar-responsavel-tecnico\": \"Inclua o Responsável Técnico clicando no botão Adicionar\", \"mensagem-validar-valor-cub\": \"Necessário informar valor para o CUB.\", \"mensagem-laudo-informado-sobrepondo-cadastrado\": \"O Laudo informado está sobrepondo um laudo ja cadastrado que possui destinação vinculada. Deseja continuar?\", \"mensagem-responsaveis-nota-tecnica-homologacao-removidos-nao-usuarios-sistema\": \"Os responsáveis pela nota técnica de homologação abaixo serão removidos pois não são usuários do sistema:\", \"mensagem-deseja-alterar-nota-tecnica-homologacao\": \"Deseja alterar o tipo da Nota Técnica de Homologação?\", \"mensagem-rascunho-salvo-com-sucesso-continuar-preenchimento\": \"Rascunho salvo com sucesso. Deseja continuar o preenchimento?\", \"mensagem-adicionar-laudo-avaliacao\": \"Necessário adicionar um laudo de avaliação.\", \"mensagem-adicionar-relatorio-valor-referencia\": \"Necessário adicionar um relatório de valor de referência.\", \"mensagem-valor-benfeitoria-nulo\":\" Valor da benfeitoria não pode ser nulo.\" }");
$templateCache.put("scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoAvaliacao.html","<form name=formularioDadosLaudo novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina ng-show=!detalharLaudoRelatorio>{{\'title-inserir-dados-laudo-avaliacao\' | translate}}</h2><h2 class=titulo-pagina ng-show=detalharLaudoRelatorio>{{\'title-detalhar-laudo-avaliacao\' | translate}}</h2></div></div><!-- Dados do imóvel --><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-rip\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column class=m-b-20><md-input-container class=md-block flex=50><div><a href=\"\" ng-click=\"ripImovelLink(imovel.rip || rip);\" target=_blank class=rip-link>{{ imovel.rip || rip }}</a></div></md-input-container></div><div ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.dataCancelamento\"><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar>{{\'title-dados-cancelamento\' | translate}}<md-input-container ng-if=\"laudoAvaliacao.situacaoLaudo==\'PENDENTE_VALIDACAO_CHEFIA\'\" flex=4><b style=\"margin-left:20px;font-size: 14px;color: #d41515 !important\">{{\'title-pendencia-validacao\' | translate}}</b></md-input-container></h3></div></div><div layout=row layout-xs=column><md-container flex=50 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row flex=100><md-datepicker ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.dataCancelamento name=\"{{nomeCampo}} - {{\'label-data\' | translate}}\" md-placeholder=\"{{\'placeholder-data\' | translate}}\" class=datePickerClass></md-datepicker></div></md-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-responsavel\' | translate}}:</label><input type=text ng-model=laudoAvaliacao.responsavelCancelamento ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-motivo-cancelamento\' | translate}}:</label><input type=text ng-model=listaMotivosCancelamento[laudoAvaliacao.motivoCancelamento] ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-justificativa\' | translate}}</label><textarea rows=4 cols=50 ng-model=laudoAvaliacao.justificativaCancelamento ng-disabled=detalharLaudoRelatorio></textarea></md-input-container></div></div><detalhar-revalidacao ng-if=\"detalharLaudoRelatorio && verificarRevalidacoes(laudoAvaliacao)\" laudo-avaliacao=laudoAvaliacao></detalhar-revalidacao><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar>{{\'title-dados-laudo\' | translate}}</h3></div></div><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-tipo-avaliacao\' | translate}}:*</span></span></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-laudo-elaborado\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50><div><b><span>{{listaTiposAvaliacao[laudoAvaliacao.tipoAvaliacao]}}</span></b></div></md-input-container><md-input-container class=md-block flex=50><md-select ng-model=laudoAvaliacao.tipoLaudoElaborado name=\"{{nomeCampo}} - Tipo Laudo Elaborado\" required ng-disabled=\"detalharLaudoRelatorio || !laudoAvaliacao.tipoAvaliacao\" aria-label=\"Tipo Laudo Elaborado\"><md-option ng-repeat=\"(k,v) in listaElaboracaoLaudo\" ng-value=k ng-if=\"detalharLaudoRelatorio || k != \'CADASTRADO_SISTEMA\'\">{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column ng-if=detalharLaudoRelatorio><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-prazo-validade\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column class=m-b-20 ng-if=detalharLaudoRelatorio><md-input-container class=md-block flex=50><div><b><span>{{laudoAvaliacao.inicioPeriodo | date: \'dd/MM/yyyy\' : \'-0300\'}} a {{laudoAvaliacao.finalPeriodo | date: \'dd/MM/yyyy\' : \'-0300\'}}</span></b></div></md-input-container></div><div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 ng-if=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_RELATORIO_VALOR_REFERENCIA\'\"><label>{{\'label-tipo-metodologia\' | translate}}:</label><md-select id=tipoMetodologia ng-model=laudoAvaliacao.tipoMetodologia name=\"{{nomeCampo}} - Metodologia\" ng-required=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_RELATORIO_VALOR_REFERENCIA\'\" ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"(k,v) in listaTiposMetodologia\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=100 ng-if=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_AVALIACAO\'\"><label>{{\'label-tipo-metodo-avaliacao\' | translate}}:</label><md-select id=tipoMetodosAvaliacao ng-model=laudoAvaliacao.tipoMetodoAvaliacao name=\"{{nomeCampo}} - Método de avaliação\" ng-required=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_AVALIACAO\'\" ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"(k,v) in listaTiposMetodosAvaliacao\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div layout=column layout-xs=column ng-show=\"laudoAvaliacao.tipoLaudoElaborado != \'CADASTRADO_SISTEMA\'\"><md-input-container class=md-block flex=100><label>{{\'label-unidade\' | translate}}:</label><md-select id=unidade ng-model=laudoAvaliacao.unidade name=\"{{\'label-unidade\' | translate}}\" ng-required=true ng-disabled=\"detalharLaudoRelatorio || listaUnidades.length == 1\"><md-option ng-repeat=\"item in listaUnidades\" ng-value=item>{{item}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-subunidade\' | translate}}</label><input type=text maxlength=150 ng-model=laudoAvaliacao.subunidade ng-disabled=detalharLaudoRelatorio></md-input-container></div><fieldset ng-init=\"laudoAvaliacao.tipoArquivo=\'UPLOAD_ARQUIVO\'\" class=fieldsetArredondado layout=column layout-xs=column ng-show=\"laudoAvaliacao.tipoLaudoElaborado != \'CADASTRADO_SISTEMA\'\"><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'label-laudo-avaliacao\' | translate}}{{ !(detalharLaudoRelatorio && laudoAvaliacao.tipoArquivo == DOCUMENTO_SEI) && (\' – \' + (\'label-upload-arquivo\' | translate)) || \'\'}}</h3></div><div layout=column flex ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.tipoArquivo == DOCUMENTO_SEI\"><label class=sidenavTitulos>{{\'label-selecione\' | translate}}*</label><md-radio-group class=m-t-10 flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=laudoAvaliacao.tipoArquivo ng-disabled=detalharLaudoRelatorio ng-change=limparArquivosLaudoReferencia() ng-required=true><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=10 ng-value=UPLOAD_ARQUIVO><div class=texto-preto>{{\'label-upload-arquivo\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=15 ng-value=DOCUMENTO_SEI><div class=texto-preto>{{\'label-utilizar-documento-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><documento-sei ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.tipoArquivo == DOCUMENTO_SEI\" detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=laudoAvaliacao.arquivosLaudoReferencia titulo=\"\'label-incluir-documentos\' | translate\" uf=imovel.enderecamento.uf></documento-sei><div ng-if=\"laudoAvaliacao.tipoArquivo == \'UPLOAD_ARQUIVO\'\"><documento-laudo-avaliacao-padrao lista-documentos-laudo=laudoAvaliacao.arquivosLaudoReferencia lista-documentos-aux=laudoAvaliacao.arquivosNotaTecnicaHomologacao detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-validacao=\"\'CONTENT_TYPE_04\'\"></documento-laudo-avaliacao-padrao><div ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.tipoArquivo == DOCUMENTO_SEI\"><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=laudoAvaliacao.laudoAvaliacaoDocumentoSei.isEnviarDocumento ng-change=changeLaudoAvaliacaoDocumentoSei() ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-enviar-documento-para-sei\' | translate}}</label></md-checkbox></div><div ng-if=laudoAvaliacao.laudoAvaliacaoDocumentoSei.isEnviarDocumento layout=column flex><md-radio-group flex class=\"m-l-30 tamanho-radio-button-avaliacao md-block\" ng-model=laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento ng-disabled=\"detalharLaudoRelatorio || !laudoAvaliacao.laudoAvaliacaoDocumentoSei.isEnviarDocumento\" ng-change=changeLaudoAvaliacaoNumeroSei() ng-required=laudoAvaliacao.laudoAvaliacaoDocumentoSei.isEnviarDocumento><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'CRIAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-criar-novo-processo-sei\' | translate}}</div></md-radio-button></div><div class=\"m-b-10 m-l-60\" layout=row ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento == \'CRIAR_PROCESSO_SEI\' && laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei\"><div class=texto-preto>{{\'label-n-processo\' | translate}}{{\': \' + laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei | processoSei}}</div></div><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'UTILIZAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-utilizar-processo-existente-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><md-input-container ng-if=\"laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" class=\"md-block m-l-60\" flex=30 flex-xs=100 flex-sm=100><label>{{\'label-numero-processo-sei\' | translate}}</label><input type=text ui-mask=99999.999999/9999-99 ng-required=\"laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" ng-model=laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei ng-disabled=detalharLaudoRelatorio ng-blur=validarLaudoAvaliacaoNumeroSeiProcessoSei()></md-input-container></div></div><div ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.tipoArquivo == DOCUMENTO_SEI\"><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=laudoAvaliacao.isArquivoModelo ng-change=limparDadosArquivosModelo(); name=\"{{\'label-incluir-arquivo-modelo\' | translate}}\" ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-incluir-arquivo-modelo\' | translate}}</label><md-tooltip md-direction=top>Adicionar arquivo do software utilizado para o cálculo</md-tooltip></md-checkbox></div><div layout=column flex ng-if=laudoAvaliacao.isArquivoModelo><documento-laudo-avaliacao-padrao hint=\"\'Adicionar arquivo do software utilizado para o cálculo\'\" titulo=\"\'Arquivos do Modelo\'\" lista-documentos-laudo=laudoAvaliacao.arquivosModelo campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_03\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-avaliacao-padrao></div></div></fieldset><div ng-if=\"laudoAvaliacao.tipoLaudoElaborado != \'CADASTRADO_SISTEMA\'\"><responsavel-tecnico incluir-usuario-logado=false lista-responsaveis-tecnicos=laudoAvaliacao.responsaveis assinatura-nota-tecnica=false assinatura-laudo-relatorio=false detalhar-laudo-relatorio=detalharLaudoRelatorio descricao-hint=\"\'Autor do Laudo Avaliação\'\"></responsavel-tecnico></div><div><div class=m-t-30 style=\"margin-bottom: -30px\" layout=row layout-xs=row flex=50 flex-xs flex-sm ng-if=\"(imovel.enderecamento.pais != BRASIL && !detalharLaudoRelatorio) || (laudoAvaliacao.tipoLaudoElaborado === \'CADASTRADO_SISTEMA\' && detalharLaudoRelatorio)\"><md-input-container class=md-block flex=50 flex-xs flex-sm><label>{{\'label-moeda\' | translate}}:</label><md-select id=moeda ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.moeda name=\"{{nomeCampo}} - Moeda\"><md-option ng-value=moeda ng-repeat=\"moeda in listaMoedas\">{{moeda.nome}}</md-option></md-select></md-input-container></div><div class=m-t-30 layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-valor-minimo\' | translate}}:</label><input required ng-model=laudoAvaliacao.valorMinimo ui-mask-placeholder-char=space maxlength=20 ng-disabled=detalharLaudoRelatorio name=\"{{nomeCampo}} - {{\'title-valor-minimo\' | translate}}\" ui-number-mask=2 ng-blur=validarValoresInformados()></md-input-container><label ng-bind=\"(laudoAvaliacao.valorMinimo > 0 ? convertNumeroPorExtenso(laudoAvaliacao.valorMinimo) : \'\')\"></label></div><div class=m-b-10 layout=row layout-xs=column ng-show=\"laudoAvaliacao.valorMinimo && (imovel.tipoImovel.descricao == \'Gleba/Terreno/Lote sem edificação\' || imovel.tipoImovel.descricao == \'Unidade autônoma\')\"><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((laudoAvaliacao.valorMinimo/areaTerrenoImovel) | currency).replace(\'R$\', \'\')}} {{laudoAvaliacao.moeda ? laudoAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-valor-medio\' | translate}}:</label><input required ng-model=laudoAvaliacao.valorMedio ui-mask-placeholder-char=space maxlength=20 ng-disabled=detalharLaudoRelatorio name=\"{{nomeCampo}} - {{\'title-valor-medio\' | translate}}\" ui-number-mask=2 ng-blur=validarValoresInformados()></md-input-container><label ng-bind=\"(laudoAvaliacao.valorMedio > 0 ? convertNumeroPorExtenso(laudoAvaliacao.valorMedio) : \'\')\"></label></div><div class=m-b-10 layout=row layout-xs=column ng-show=\"laudoAvaliacao.valorMedio && (imovel.tipoImovel.descricao == \'Gleba/Terreno/Lote sem edificação\' || imovel.tipoImovel.descricao == \'Unidade autônoma\')\"><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((laudoAvaliacao.valorMedio/areaTerrenoImovel) | currency).replace(\'R$\', \'\')}} {{laudoAvaliacao.moeda ? laudoAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-valor-maximo\' | translate}}:</label><input required ng-model=laudoAvaliacao.valorMaximo ui-mask-placeholder-char=space maxlength=20 ng-disabled=detalharLaudoRelatorio name=\"{{nomeCampo}} - {{\'title-valor-maximo\' | translate}}\" ui-number-mask=2 ng-blur=validarValoresInformados()></md-input-container><label ng-bind=\"(laudoAvaliacao.valorMaximo > 0 ? convertNumeroPorExtenso(laudoAvaliacao.valorMaximo) : \'\')\"></label></div><div layout=row layout-xs=column ng-show=\"laudoAvaliacao.valorMaximo && (imovel.tipoImovel.descricao == \'Gleba/Terreno/Lote sem edificação\' || imovel.tipoImovel.descricao == \'Unidade autônoma\')\"><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((laudoAvaliacao.valorMaximo/areaTerrenoImovel) | currency).replace(\'R$\', \'\')}} {{laudoAvaliacao.moeda ? laudoAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><label class=\"sidenavTitulos deslocamentoTituloData font\">{{\'title-valor-adotado\' | translate}}:</label><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=laudoAvaliacao.valorAdotadoTemp name=\"{{nomeCampo}} - {{\'title-valor-adotado\' | translate}}\" ng-change=changeValoresDataValor()><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"tipoValor in listaTiposValores | orderBy: \'ordem\'\" ng-value=tipoValor.name><label class=texto-preto>{{tipoValor.descricao}}</label></md-radio-button></div></md-radio-group></md-input></div><div class=m-t-10 layout=row layout-xs=row flex=100 flex-xs flex-sm ng-if=\"laudoAvaliacao.valorAdotado==\'OUTRO_VALOR\'\"><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-outro-valor-adotado\' | translate}}:</label><input required ng-model=laudoAvaliacao.outroValorAdotadoTemp ui-mask-placeholder-char=space maxlength=20 ng-disabled=detalharLaudoRelatorio name=\"{{nomeCampo}} - {{\'title-outro-valor-adotado\' | translate}}\" ui-number-mask=2 ng-blur=changeValoresDataValor()></md-input-container><label ng-bind=\"(laudoAvaliacao.outroValorAdotadoTemp > 0 ? convertNumeroPorExtenso(laudoAvaliacao.outroValorAdotadoTemp) : \'\')\"></label></div><div layout=row layout-xs=column ng-show=\"laudoAvaliacao.outroValorAdotadoTemp && (imovel.tipoImovel.descricao == \'Gleba/Terreno/Lote sem edificação\' || imovel.tipoImovel.descricao == \'Unidade autônoma\')\"><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((laudoAvaliacao.outroValorAdotado/areaTerrenoImovel) | currency).replace(\'R$\', \'\')}} {{laudoAvaliacao.moeda ? laudoAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout=row layout-xs=column><div class=\"md-block m-t-10\" flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-data-de-assinatura\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column flex=100><md-container flex=50 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row><md-datepicker ng-required=!(detalharLaudoRelatorio) ng-if=!detalharLaudoRelatorio ng-model=laudoAvaliacao.dataAssinaturaTemp name=\"{{nomeCampo}} - {{\'label-data-de-assinatura\' | translate}}\" md-placeholder=\"{{\'placeholder-data-assinatura\' | translate}}*\" class=datePickerClass ng-change=changeValoresDataValor();></md-datepicker><span ng-if=detalharLaudoRelatorio class=\"m-t-10 texto-preto\">{{laudoAvaliacao.dataAssinaturaTemp | date: \'dd/MM/yyyy\' : \'-0300\'}}</span></div></md-container></div><div ng-if=\"laudoAvaliacao.dataAssinatura && laudoAvaliacao.valorImovel\"><div layout=row layout-xs=column ng-if=exibirInserirValoresTerrenoBenfeitoria()><div ng-if=\"(detalharLaudoRelatorio && laudoAvaliacao.tipoValorLaudo != null) || !detalharLaudoRelatorio\" flex><div layout=row layout-xs=column ng-if=exibirInserirValoresTerrenoBenfeitoria()><div flex><h3 class=subtitulo-pagina-detalhar style=\"padding-top: 10px\">{{\'valores-terreno-benfeitoria\' | translate}}</h3></div></div><inserir-valores-terreno-benfeitoria lista-benfeitorias-imovel=laudoAvaliacao.benfeitoriasImovel moeda=laudoAvaliacao.moeda unidade-autonoma-imovel=laudoAvaliacao.unidadeAutonomaIncorporacao tipo-valor-laudo=laudoAvaliacao.tipoValorLaudo tipo-laudo=LAUDO_AVALIACAO detalhar-laudo-relatorio=\"detalharLaudoRelatorio || laudoAvaliacao.laudoRelatorioAvaliacao\" redirecionar-informar-valor-cub=redirecionarInformarValorCub validar-valor-imovel=validarValorImovel rip-imovel=rip total-valor-terreno=laudoAvaliacao.valorTotalTerreno valor-adotado-selecionado=laudoAvaliacao.valorImovel assinatura=laudoAvaliacao.dataAssinatura valor-terreno=laudoAvaliacao.valorTerreno unidade-medida=unidadeMedida lista-moedas=listaMoedas pais-imovel=imovel.enderecamento.pais termo-responsabilidade=laudoAvaliacao.termoResponsabilidade></inserir-valores-terreno-benfeitoria></div><div ng-if=\"laudoAvaliacao.tipoValorLaudo == null && detalharLaudoRelatorio && laudoAvaliacao.tipoLaudoElaborado == \'CADASTRADO_SISTEMA\' && laudoAvaliacao.benfeitoriasImovel[0].valor\" flex><div layout=row layout-xs=column ng-if=exibirInserirValoresTerrenoBenfeitoria()><div flex><h3 class=subtitulo-pagina>{{\'valores-terreno-benfeitoria\' | translate}}</h3></div></div><div layout=column><label class=\"sidenavTitulos marginTopHomologadorNome\" layout=row>{{\'label-valor-terreno\' | translate}}:</label><div layout=row>{{\"R\"}}{{moeda ? moeda.simbolo : listaMoedas[0].simbolo}} {{(laudoAvaliacao.valorTerreno | currency).replace(\'R$\',\'\')}}</div></div><table-estimar-valor-terreno-laudo-benfeitorias nome-campo=nomeCampo benfeitorias-imovel=laudoAvaliacao.benfeitoriasImovel detalhar-laudo-relatorio=detalharLaudoRelatorio redirecionar-informar-valor-cub=redirecionarInformarValorCub tipo-valor-laudo=tipoValorLaudo moeda=moeda lista-moedas=listaMoedas></table-estimar-valor-terreno-laudo-benfeitorias></div></div><!--                 <div ng-if=\"(laudoAvaliacao.tipoValorLaudo == ESTIMAR_VALORES) && false\">\n                    <md-input layout=\"column\" class=\"md-block\" flex=\"100\">\n                        <label>{{\'label-fonte-valor-terreno\' | translate}}:</label>\n                        <md-radio-group class=\"tamanho-radio-button-avaliacao\" ng-model=\"fonteValorTerreno\"\n                                        name=\"{{nomeCampo}} - {{\'label-fonte-valor-terreno\' | translate}}\" ng-disabled=\"detalharLaudoRelatorio\">\n                            <div layout=\"row\">\n                                <md-radio-button ng-repeat=\"(k,v) in listaFonteValorTerreno\" layout-align=\"space-between\" class=\"tamanho-radio-button-avaliacao md-primary\"\n                                                 flex=\"20\" ng-value=\"k\" ng-if=\"k != REFERENCIA_INCRA || laudoAvaliacao.laudoRelatorioAvaliacao.detalhesImovel.imovel.naturezaImovel == RURAL\">\n                                    <div class=\"texto-preto\">{{v}}</div>\n                                </md-radio-button>\n                            </div>\n                        </md-radio-group>\n                    </md-input>\n                    <br>\n                    <md-input layout=\"row\" class=\"md-block\">\n                        {{\'label-valor-terreno-rs\' | translate}}: {{laudoAvaliacao.valorTerreno | currency}}\n                    </md-input>\n                    <br>\n                    <md-input layout=\"row\" class=\"md-block\">\n                        {{\'label-valor-m2\' | translate}}: {{laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.valorTerreno && laudoAvaliacao.areaTerreno ? laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.valorTerreno / laudoAvaliacao.areaTerreno : \'\' | currency}}\n                    </md-input>\n                    <br>\n                    <md-input layout=\"row\" class=\"md-block\">\n                        {{\'label-periodo-avaliacao\' | translate}}: {{laudoAvaliacao.laudoRelatorioAvaliacao.periodoInicial | date: \'dd/MM/yyyy\' : \'-0300\'}} até {{laudoAvaliacao.laudoRelatorioAvaliacao.periodoFinal | date: \'dd/MM/yyyy\' : \'-0300\'}}\n                    </md-input>\n                    <br>\n                    <div layout=\"row\" flex=\"100\">\n                        <md-input-container layout=\"row\" class=\"md-block\" flex=\"50\">\n                            <label>{{\'label-valor-terreno\' | translate}}:</label>\n                            <input type=\"value\" ng-model=\"valorTerreno\" name=\"{{nomeCampo}} - {{\'label-valor-terreno\' | translate}}\" ui-number-mask=\"2\"\n                                   ng-disabled=\"detalharLaudoRelatorio\"/>\n                        </md-input-container>\n                        <md-input-container flex=\"50\">{{valorTerreno ? convertNumeroPorExtenso(valorTerreno) : null}}</md-input-container>\n                    </div>\n                    <md-input-container layout=\"row\" class=\"md-block\">\n                        <label>{{\'label-fonte\' | translate}}:</label>\n                        <textarea class=\"barraRolagemTextArea\" ng-model=\"avaliacao.pesquisaMercado\" ui-mask-placeholder-char=\"space\"\n                                  name=\"{{nomeCampo}} - {{\'label-fonte\' | translate}}\" ng-disabled=\"detalharLaudoRelatorio\"></textarea>\n                    </md-input-container>\n                </div> --></div><!--div layout=\"row\" layout-xs=\"column\" class=\"m-b-10 m-t-10\" ng-if=\"laudoAvaliacao.tipoLaudoElaborado == POR_TERCEIROS\">\n                <md-checkbox class=\"md-primary\" ng-model=\"laudoAvaliacao.isNaoHomologacao\" ng-change=\"limparDadosHomologacao();\" name=\"{{\'label-nao-necessita-homologacao\' | translate}}\" ng-disabled=\"detalharLaudoRelatorio\">\n                    <label class=\"text-black\">{{\'label-nao-necessita-homologacao\' | translate}}</label>\n                </md-checkbox>\n            </div--><!--div layout=\"row\" layout-xs=\"column\" ng-if=\"laudoAvaliacao.tipoLaudoElaborado == POR_TERCEIROS && laudoAvaliacao.isNaoHomologacao == true\">\n                <md-input-container class=\"md-block\" flex=\"100\" flex-xs=\"100\" flex-sm=\"100\">\n                    <label>{{\'label-justificativa-nao-homologacao\' | translate}}:</label>\n                    <textarea class=\"barraRolagemTextArea\" ng-model=\"laudoAvaliacao.justificativaNaoHomologacao\" ng-required=\"laudoAvaliacao.isNaoHomologacao == true\" ui-mask-placeholder-char=\"space\"\n                              name=\"{{nomeCampo}} - {{\'label-justificativa-nao-homologacao\' | translate}}\" ng-disabled=\"detalharLaudoRelatorio\"></textarea>\n                </md-input-container>\n            </div--><responsavel-tecnico ng-if=laudoAvaliacao.tipoNotaTecnicaHomologacao incluir-usuario-logado=false lista-responsaveis-tecnicos=laudoAvaliacao.homologadores custom-title=\"\'label-responsaveis-nota-tecnica-homologacao\'\" assinatura-nota-tecnica=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == PREENCHER_SISTEMA\" assinatura-laudo-relatorio=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == PREENCHER_SISTEMA\" detalhar-laudo-relatorio=detalharLaudoRelatorio></responsavel-tecnico><!--div ng-if=\"laudoAvaliacao.tipoLaudoElaborado == POR_TERCEIROS && !laudoAvaliacao.isNaoHomologacao\">\n\n                <div layout=\"row\" layout-xs=\"column\">\n\n                    <div class=\"md-block\" flex-gt-xs flex=\"100\" style=\"margin-top: -15px\">\n                    <span class=\"datepicker-label\">\n                        <span>{{\'label-nota-tecnica-homologacao\' | translate}}:*</span>\n                    </span>\n                    </div>\n\n                </div>\n\n                <div layout=\"row\" layout-xs=\"column\">\n\n                    <md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=\"laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp\" ng-change=\"limpartipoNotaTecnicaHomologacao()\" name=\"{{nomeCampo}} - {{\'label-utilizar-laudo-preenchido\' | translate}}\" ng-disabled=\"detalharLaudoRelatorio\">\n\n                        <md-radio-button ng-repeat=\"(k,v) in listaTipoNotaTecnicaHomologacao\" flex=\"33\" flex-xs=\"100\" flex-sm=\"100\" flex-md=\"100\" class=\"tamanho-radio-button-avaliacao md-primary m-r-20\"\n                                         ng-value=\"k\"><label class=\"texto-preto\">{{v}}</label></md-radio-button>\n\n                    </md-radio-group>\n\n                </div>\n\n                <div ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == PREENCHER_SISTEMA\">\n                    <md-button style=\"font-weight:normal; text-transform:none;\" class=\"md-primary md-raised no-margin\" ng-click=\"inserirNotaTecnicaHomologacao()\" ng-if=\"!laudoAvaliacao.notaTecnicaHomologacao\">\n                        <label>{{\'label-preencher-alterar-nota\' | translate}}</label>\n                    </md-button>\n\n                    <div layout=\"row\" layout-align=\"center start\" ng-if=\"laudoAvaliacao.notaTecnicaHomologacao && !detalharLaudoRelatorio\">\n                        <div flex>\n                            <md-table-container>\n                                <table md-table>\n                                    <thead md-head>\n                                    <tr md-row>\n                                        <th md-column>{{\'label-nome\' | translate}}</th>\n                                        <th md-column>{{\'label-situacao\' | translate}}</th>\n                                        <th md-column>{{\'label-acoes\' | translate}}</th>\n                                    </tr>\n                                    </thead>\n                                    <tbody md-body>\n                                    <tr md-row>\n                                        <td md-cell>\n                                            <a herf=\"\">{{\'label-preencher-alterar-nota\' | translate}}</a>\n                                        </td>\n                                        <td md-cell ng-style=\"laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento ? {} : labelVermelho\">\n                                            <label ng-bind=\"laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento ? (\'label-preenchida-em\' | translate) + \' \' + (laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento | date: \'dd/MM/yyyy\' : \'-0300\') : (\'label-preenchimento-incompleto\' | translate)\"></label>\n                                        </td>\n                                        <td md-cell>\n                                            <div class=\"container-acoes\">\n                                                <md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=\"left\">\n                                                    <md-fab-trigger>\n                                                        <md-button aria-label=\"menu\" class=\"md-fab\">\n                                                            <md-icon ng-bind=\"\'more_vert\'\"></md-icon>\n                                                            <md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip>\n                                                        </md-button>\n                                                    </md-fab-trigger>\n\n                                                    <md-fab-actions>\n                                                        <md-button ng-click=\"inserirNotaTecnicaHomologacao()\" class=\"md-fab md-raised md-mini\">\n                                                            <md-icon alt=\"{{\'label-editar\' | translate}}\" ng-bind=\"\'edit\'\" aria-label=\"{{\'label-editar\' | translate}}\">\n                                                                <md-tooltip>{{\'label-editar\' | translate}}</md-tooltip>\n                                                            </md-icon>\n                                                        </md-button>\n                                                        <md-button ng-click=\"limparNotaTecnica()\" class=\"md-fab md-raised md-mini\">\n                                                            <md-icon alt=\"{{\'label-excluir\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir\' | translate}}\">\n                                                                <md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip>\n                                                            </md-icon>\n                                                        </md-button>\n                                                    </md-fab-actions>\n                                                </md-fab-speed-dial>\n                                            </div>\n                                        </td>\n                                    </tr>\n                                    </tbody>\n                                </table>\n                            </md-table-container>\n                        </div>\n                    </div>\n\n                    <div layout=\"row\" layout-align=\"center start\" ng-if=\"laudoAvaliacao.notaTecnicaHomologacao && detalharLaudoRelatorio\">\n                        <div flex>\n                            <md-table-container>\n                                <table md-table>\n                                    <thead md-head>\n                                    <tr md-row>\n                                        <th md-column>{{\'label-nome\' | translate}}</th>\n                                    </tr>\n                                    </thead>\n                                    <tbody md-body>\n                                    <tr md-row>\n                                        <td md-cell>\n                                            <a ng-show=\"laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_CHEFIA\' && laudoAvaliacao.situacaoLaudo != \'EM_PROCESSO_HOMOLOGACAO\' && laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_TECNICO\' && laudoAvaliacao.situacaoLaudo != \'PENDENTE_HOMOLOGACAO\' && laudoAvaliacao.situacaoLaudo != \'EM_PREENCHIMENTO\'\"\n                                               class=\"mouse-pointer\" ng-href=\"{{downloadNotaTecnicaHomologacao()}}\" target=\"_blank\">Nota Técnica de Homologação</a>\n                                            <a ng-show=\"(laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_CHEFIA\' || laudoAvaliacao.situacaoLaudo == \'EM_PROCESSO_HOMOLOGACAO\' || laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_TECNICO\' || laudoAvaliacao.situacaoLaudo == \'PENDENTE_HOMOLOGACAO\' || laudoAvaliacao.situacaoLaudo == \'EM_PREENCHIMENTO\') && !laudoAvaliacao.revalidacaoLaudoAvaliacao\"\n                                               class=\"mouse-pointer\" href=\"\" ng-click=\"visualizarDocumentoNotaTecnica()\" target=\"_blank\">Nota Técnica de Homologação</a>\n                                        </td>\n                                    </tr>\n                                    </tbody>\n                                </table>\n                            </md-table-container>\n                        </div>\n                    </div>\n                </div>\n\n                <div ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == UPLOAD_ARQUIVO\">\n                    <documento-laudo-avaliacao-padrao lista-documentos-laudo=\"laudoAvaliacao.arquivosNotaTecnicaHomologacao\"\n                                                      lista-documentos-aux=\"laudoAvaliacao.arquivosLaudoReferencia\"\n                                                      campo-data=\"\'true\'\"\n                                                      tipo-validacao=\"\'CONTENT_TYPE_01\'\"\n                                                      titulo=\"\'Nota Técnica de Homologação\'\"\n                                                      detalhar-laudo-avaliacao=\"detalharLaudoRelatorio\"></documento-laudo-avaliacao-padrao>\n                </div>\n                <div ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == UPLOAD_ARQUIVO || laudoAvaliacao.tipoNotaTecnicaHomologacao == PREENCHER_SISTEMA\">\n                    <div layout=\"row\" layout-xs=\"column\" class=\"m-t-10\">\n                        <md-checkbox class=\"md-primary\" ng-model=\"laudoAvaliacao.notaTecnicaDocumentoSei.isEnviarDocumento\" ng-change=\"changeNotaTecnicaDocumentoSei()\" ng-disabled=\"detalharLaudoRelatorio\">\n                            <label class=\"text-black\">{{\'label-enviar-documento-para-sei\' | translate}}</label>\n                        </md-checkbox>\n                    </div>\n\n                    <div ng-if=\"laudoAvaliacao.notaTecnicaDocumentoSei.isEnviarDocumento\" layout=\"column\" flex>\n                        <md-radio-group flex class=\"m-l-30 tamanho-radio-button-avaliacao md-block\"\n                                        ng-model=\"laudoAvaliacao.notaTecnicaDocumentoSei.tipoEnviarDocumento\" ng-disabled=\"detalharLaudoRelatorio || !laudoAvaliacao.notaTecnicaDocumentoSei.isEnviarDocumento\" ng-change=\"limparNotaTecnicaNumeroSei()\" ng-required=\"laudoAvaliacao.notaTecnicaDocumentoSei.isEnviarDocumento\">\n                            <div layout=\"row\">\n                                <md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=\"100\"\n                                                 ng-value=\"\'CRIAR_PROCESSO_SEI\'\">\n                                    <div class=\"texto-preto\">{{\'label-criar-novo-processo-sei\' | translate}}</div>\n                                </md-radio-button>\n                            </div>\n                            <div class=\"m-b-10 m-l-60\" layout=\"row\" ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.notaTecnicaDocumentoSei.tipoEnviarDocumento == \'CRIAR_PROCESSO_SEI\' && laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei\">\n                                <div class=\"texto-preto\">{{\'label-n-processo\' | translate}}{{\': \' + laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei | processoSei}}</div>\n                            </div>\n                            <div layout=\"row\">\n                                <md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=\"100\"\n                                                 ng-value=\"\'UTILIZAR_PROCESSO_SEI\'\">\n                                    <div class=\"texto-preto\">{{\'label-utilizar-processo-existente-sei\' | translate}}</div>\n                                </md-radio-button>\n                            </div>\n                        </md-radio-group>\n                    </div>\n\n                    <md-input-container ng-if=\"laudoAvaliacao.notaTecnicaDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" class=\"md-block m-l-60\" flex=\"30\" flex-xs=\"100\" flex-sm=\"100\">\n                        <label>{{\'label-numero-processo-sei\' | translate}}</label>\n                        <input type=\"text\" ui-mask=\"99999.999999/9999-99\" ng-required=\"laudoAvaliacao.notaTecnicaDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" ng-model=\"laudoAvaliacao.notaTecnicaDocumentoSei.numeroProcessoSei\" ng-disabled=\"detalharLaudoRelatorio\" ng-blur=\"validarNotaTecnicaNumeroSeiProcessoSei()\"/>\n                    </md-input-container>\n                </div>\n\n                <documento-sei ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == DOCUMENTO_SEI\"\n                               detalhar-laudo-avaliacao=\"detalharLaudoRelatorio\"\n                               lista-documentos-laudo=\"laudoAvaliacao.arquivosNotaTecnicaHomologacao\"\n                               titulo=\"\'label-incluir-documentos\' | translate\"\n                               uf=\"imovel.enderecamento.uf\"></documento-sei>\n\n                <div ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == UPLOAD_ARQUIVO || laudoAvaliacao.tipoNotaTecnicaHomologacao == DOCUMENTO_SEI\" layout=\"row\" layout-xs=\"column\" flex-sm=\"column\">\n\n                    <md-input class=\"md-block\" flex=\"100\" flex-xs=\"100\" flex-sm=\"100\">\n                        <div layout=\"row\">\n                            <label>{{\'label-parecer-tecnico\' | translate}}:*</label>\n                        </div>\n                        <md-radio-group class=\"tamanho-radio-button-avaliacao\" ng-model=\"laudoAvaliacao.homologacao\" required=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == UPLOAD_ARQUIVO && !laudoAvaliacao.isNaoHomologacao\" ng-disabled=\"detalharLaudoRelatorio\">\n                            <div layout=\"row\" class=\"m-t-20\">\n                                <md-radio-button layout-align=\"space-between\" class=\"tamanho-radio-button-avaliacao md-primary\" flex=\"100\" ng-value=\"true\">\n                                    <div class=\"texto-preto\">Sugiro pela <b>homologação</b> do laudo de avaliação</div>\n                                </md-radio-button>\n                            </div>\n                            <div layout=\"row\">\n                                <md-radio-button layout-align=\"space-between\" class=\"tamanho-radio-button-avaliacao md-primary\" flex=\"100\" ng-value=\"false\">\n                                    <div class=\"texto-preto\">Sugiro pela <b>não homologação</b> do laudo de avaliação</div>\n                                </md-radio-button>\n                            </div>\n                        </md-radio-group>\n                    </md-input>\n                </div>\n\n            </div--></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div class=m-b-20 layout=row flex=100 ng-show=!detalharLaudoRelatorio><div flex=100 layout=column><md-checkbox class=md-primary ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.termoResponsabilidade.isAssinado name=\"{{\'label-termo-responsabilidade\' | translate}}\" ng-required=true aria-label=\"Assinatura do termo de responsabilidade\"><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_AVALIACAO\">{{\'Eu, \' + usuarioLogado.nome + \', CPF: \' + (usuarioLogado.cpf | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-laudo-avaliacao\' | translate}}</label><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_RELATORIO_VALOR_REFERENCIA\">{{\'Eu, \' + usuarioLogado.nome + \', CPF: \' + (usuarioLogado.cpf | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-relatorio-valor-referencia\' | translate}}</label></md-checkbox></div></div><!-- Enquanto o laudo estiver na situação \"Em Preenchimento\" não é possível exibir os dados do aceite, pois o atributo \"termoResponsabilidade\" é nulo --><div class=m-b-20 layout=row flex=100 ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.termoResponsabilidade !== null && laudoAvaliacao.situacaoLaudo !== \'EM_PREENCHIMENTO\'\"><div flex=100 layout=column><md-checkbox class=md-primary ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.termoResponsabilidade.isAssinado name=\"{{\'label-termo-responsabilidade\' | translate}}\" aria-label=\"Assinatura do termo de responsabilidade\"><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_AVALIACAO\">{{\'Eu, \' + laudoAvaliacao.termoResponsabilidade.nomeAssinador + \', CPF: \' + (laudoAvaliacao.termoResponsabilidade.cpfAssinador | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-laudo-avaliacao\' | translate}}</label><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_RELATORIO_VALOR_REFERENCIA\">{{\'Eu, \' + laudoAvaliacao.termoResponsabilidade.nomeAssinador + \', CPF: \' + (laudoAvaliacao.termoResponsabilidade.cpfAssinador | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-relatorio-valor-referencia\' | translate}}</label></md-checkbox></div></div><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarComoRascunhoLaudoAvaliacao() ng-if=!detalharLaudoRelatorio><label>{{\'button-salvar-rascunho\' | translate}}</label></md-button><md-button ng-if=\"!detalharLaudoRelatorio && laudoAvaliacao.tipoNotaTecnicaHomologacao == PREENCHER_SISTEMA && verificarHomologador()\" class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarLaudoAvaliacao(formularioDadosLaudo)><label>{{\'button-gravar-assinar-nota-tecnica\' | translate}}</label></md-button><md-button ng-if=\"!detalharLaudoRelatorio && laudoAvaliacao.tipoNotaTecnicaHomologacao != PREENCHER_SISTEMA || (!detalharLaudoRelatorio && !verificarHomologador())\" class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarLaudoAvaliacao(formularioDadosLaudo)><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoAvaliacaoLocacao.html","<form name=formularioDadosLaudo novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina ng-show=!detalharLaudoRelatorio>{{\'title-inserir-dados-laudo-avaliacao-locacao\' | translate}}</h2><h2 class=titulo-pagina ng-show=detalharLaudoRelatorio>{{\'title-detalhar-laudo-avaliacao-locacao\' | translate}}</h2></div></div><div ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.dataCancelamento\" layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar>{{\'title-dados-cancelamento\' | translate}}</h3></div></div><div layout=row layout-xs=column ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.dataCancelamento\"><md-container flex=50 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row flex=100><md-datepicker ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.dataCancelamento name=\"{{nomeCampo}} - {{\'label-data\' | translate}}\" md-placeholder=\"{{\'placeholder-data\' | translate}}\" class=datePickerClass></md-datepicker></div></md-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-responsavel\' | translate}}:</label><input type=text ng-model=laudoAvaliacao.responsavelCancelamento ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=row layout-xs=column ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.dataCancelamento\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-motivo-cancelamento\' | translate}}:</label><input type=text ng-model=listaMotivosCancelamento[laudoAvaliacao.motivoCancelamento] ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=row layout-xs=column ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.dataCancelamento\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-justificativa\' | translate}}</label><textarea rows=4 cols=50 ng-model=laudoAvaliacao.justificativaCancelamento ng-disabled=detalharLaudoRelatorio></textarea></md-input-container></div><div ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.revalidacaoLaudoAvaliacao\"><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar>{{\'title-dados-revalidacao\' | translate}}</h3></div></div><div class=m-b-20 flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th class=th-margin-pvgs md-column>{{\'label-nome-arquivo\' | translate}}</th><th ng-show=\"laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio != \'PREENCHER_SISTEMA\'\" class=th-margin-pvgs md-column>{{\'label-arquivo\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-if=\"laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio == \'PREENCHER_SISTEMA\' && detalharLaudoRelatorio\"><td md-cell><a ng-if=\"laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_CHEFIA\' && laudoAvaliacao.situacaoLaudo != \'EM_PROCESSO_HOMOLOGACAO\' && laudoAvaliacao.situacaoLaudo != \'EM_PROCESSO_REVALIDACAO\' && laudoAvaliacao.situacaoLaudo != \'REVALIDACAO_PREENCHIMENTO\'\" class=mouse-pointer ng-href={{downloadRevalidacaoLaudoAvaliacao()}} target=_blank>Nota de Revalidação</a> <a ng-if=\"laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_CHEFIA\' || laudoAvaliacao.situacaoLaudo == \'EM_PROCESSO_HOMOLOGACAO\' || laudoAvaliacao.situacaoLaudo == \'EM_PROCESSO_REVALIDACAO\' || laudoAvaliacao.situacaoLaudo == \'REVALIDACAO_PREENCHIMENTO\'\" class=mouse-pointer href=\"\" ng-click=visualizarDocumentoRevalidacaoLaudo() target=_blank>Nota de Revalidação</a></td></tr><tr md-row ng-repeat=\"anexos in laudoAvaliacao.revalidacaoLaudoAvaliacao.arquivosLaudoRevalidacao\" ng-show=\"laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio != \'PREENCHER_SISTEMA\'\"><td md-cell><label ng-bind=anexos.nome></label></td><td md-cell><a ng-href={{baixarArquivo(anexos.arquivo.id)}} target=_blank><label ng-bind=anexos.arquivo.nome></label></a></td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.isEnviarDocumento ng-change=changeLaudoAvaliacaoDocumentoSei() ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-enviar-documento-para-sei\' | translate}}</label></md-checkbox></div><div ng-if=laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.isEnviarDocumento layout=column flex><md-radio-group flex class=\"m-l-30 tamanho-radio-button-avaliacao md-block\" ng-model=laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.tipoEnviarDocumento ng-disabled=\"detalharLaudoRelatorio || !laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.isEnviarDocumento\" ng-change=changeLaudoAvaliacaoNumeroSei() ng-required=laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.isEnviarDocumento><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'CRIAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-criar-novo-processo-sei\' | translate}}</div></md-radio-button></div><div class=\"m-b-10 m-l-60\" layout=row ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.tipoEnviarDocumento == \'CRIAR_PROCESSO_SEI\' && laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei\"><div class=texto-preto>{{\'label-n-processo\' | translate}}{{\': \' + laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei | processoSei}}</div></div><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'UTILIZAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-utilizar-processo-existente-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><md-input-container ng-if=\"laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" class=\"md-block m-l-60\" flex=30 flex-xs=100 flex-sm=100><label>{{\'label-numero-processo-sei\' | translate}}</label><input type=text ui-mask=99999.999999/9999-99 ng-required=\"laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" ng-model=laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei ng-disabled=detalharLaudoRelatorio></md-input-container><div class=m-b-20 flex ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio == \'UPLOAD_ARQUIVO\'\"><div class=m-b-20 layout=row flex=100 ng-show=\"laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio == UPLOAD_ARQUIVO\"><div flex=100 layout=column><md-checkbox class=md-primary ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.revalidacaoLaudoAvaliacao.termoResponsabilidade.isAssinado name=\"{{\'label-termo-responsabilidade\' | translate}}\" ng-required=\"laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio == UPLOAD_ARQUIVO\"><label class=text-black>{{\'Eu, \' + laudoAvaliacao.revalidacaoLaudoAvaliacao.termoResponsabilidade.nomeAssinador + \', CPF: \' + (laudoAvaliacao.revalidacaoLaudoAvaliacao.termoResponsabilidade.cpfAssinador | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-laudo-avaliacao\' | translate}}</label></md-checkbox></div></div></div></div><div ng-show=detalharLaudoRelatorio layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar>{{\'title-dados-laudo\' | translate}}</h3></div></div><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-tipo-avaliacao\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50><div><b><span>{{listaTiposAvaliacao[laudoAvaliacao.tipoAvaliacao]}}</span></b></div></md-input-container><md-input-container class=md-block flex=50><label>{{\'label-laudo-elaborado\' | translate}}:</label><md-select ng-model=laudoAvaliacao.tipoLaudoElaborado name=\"{{nomeCampo}} - Tipo Laudo Elaborado\" required ng-disabled=\"detalharLaudoRelatorio || !laudoAvaliacao.tipoAvaliacao\" aria-label=\"Tipo Laudo Elaborado\"><md-option ng-repeat=\"(k,v) in listaElaboracaoLaudo\" ng-value=k ng-if=\"detalharLaudoRelatorio || k != \'CADASTRADO_SISTEMA\'\">{{v}}</md-option></md-select></md-input-container></div><div ng-show=\"laudoAvaliacao.tipoAvaliacao && laudoAvaliacao.tipoLaudoElaborado\"><div layout=row layout-xs=column><md-input-container class=md-block flex=100 ng-if=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_RELATORIO_VALOR_REFERENCIA\'\"><label>{{\'label-tipo-metodologia\' | translate}}:</label><md-select id=tipoMetodologia ng-model=laudoAvaliacao.tipoMetodologia name=\"{{nomeCampo}} - Metodologia\" ng-required=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_RELATORIO_VALOR_REFERENCIA\'\" ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"(k,v) in listaTiposMetodologia\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=100 ng-if=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_AVALIACAO_LOCACAO\'\"><label>{{\'label-tipo-metodo-avaliacao\' | translate}}:</label><md-select id=tipoMetodosAvaliacao ng-model=laudoAvaliacao.tipoMetodoAvaliacao name=\"{{nomeCampo}} - Método de avaliação\" ng-required=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_AVALIACAO_LOCACAO\'\" ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"(k,v) in listaTiposMetodosAvaliacao\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-orgao\' | translate}}:</label><input type=text ng-model=laudoAvaliacao.orgao maxlength=150 ng-required=true ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-unidade\' | translate}}:</label><input type=text ng-model=laudoAvaliacao.unidade maxlength=150 ng-required=true ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-subunidade\' | translate}}</label><input type=text maxlength=150 ng-model=laudoAvaliacao.subunidade ng-disabled=detalharLaudoRelatorio></md-input-container></div><responsavel-tecnico incluir-usuario-logado=false lista-responsaveis-tecnicos=laudoAvaliacao.responsaveis detalhar-laudo-relatorio=detalharLaudoRelatorio descricao-hint=\"\'Autor do Laudo Avaliação\'\"></responsavel-tecnico><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-data-de-assinatura\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column><md-container flex=\"{{(imovel.enderecamento.pais != BRASIL && !detalharLaudoRelatorio) || (laudoAvaliacao.tipoLaudoElaborado === \'CADASTRADO_SISTEMA\' && detalharLaudoRelatorio) ? \'33\' : \'50\'}}\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row flex=100><md-datepicker ng-required=!(detalharLaudoRelatorio) ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.dataAssinaturaTemp name=\"{{nomeCampo}} - {{\'label-data-de-assinatura\' | translate}}\" md-placeholder=\"{{\'placeholder-data-assinatura\' | translate}}*\" class=datePickerClass ng-change=changeValoresDataValor();></md-datepicker></div></md-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 ng-if=\"(imovel.enderecamento.pais != BRASIL && !detalharLaudoRelatorio) || (laudoAvaliacao.tipoLaudoElaborado === \'CADASTRADO_SISTEMA\' && detalharLaudoRelatorio)\"><label>{{\'label-moeda\' | translate}}:</label><md-select id=moeda ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.moeda name=\"{{nomeCampo}} - Moeda\"><md-option ng-value=moeda ng-repeat=\"moeda in listaMoedas\">{{moeda.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=\"{{(imovel.enderecamento.pais != BRASIL && !detalharLaudoRelatorio) || (laudoAvaliacao.tipoLaudoElaborado === \'CADASTRADO_SISTEMA\' && detalharLaudoRelatorio) ? \'33\' : \'50\'}}\" flex-xs=100 flex-sm=100><div layout=row><label>{{\'label-valor-locacao-imovel\' | translate}} {{laudoAvaliacao.moeda ? laudoAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}:</label></div><input type=value ng-model=laudoAvaliacao.valorImovelTemp ng-blur=changeValoresDataValor() name=\"{{nomeCampo}} - {{\'label-valor-imovel\' | translate}}\" ui-number-mask=2 maxlength=20 ng-required=!(detalharLaudoRelatorio) ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=row layout-xs=column class=m-b-10 ng-if=\"laudoAvaliacao.tipoLaudoElaborado == POR_TERCEIROS\"><md-checkbox class=md-primary ng-model=laudoAvaliacao.isNaoHomologacao ng-change=limparDadosHomologacao(); name=\"{{\'label-nao-necessita-homologacao\' | translate}}\" ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-nao-necessita-homologacao\' | translate}}</label></md-checkbox></div><div layout=row layout-xs=column ng-if=\"laudoAvaliacao.tipoLaudoElaborado == POR_TERCEIROS && laudoAvaliacao.isNaoHomologacao == true\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-justificativa-nao-homologacao\' | translate}}:</label><textarea class=barraRolagemTextArea ng-model=laudoAvaliacao.justificativaNaoHomologacao ng-required=\"laudoAvaliacao.isNaoHomologacao == true\" ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-justificativa-nao-homologacao\' | translate}}\" ng-disabled=detalharLaudoRelatorio></textarea></md-input-container></div><div ng-if=\"laudoAvaliacao.tipoLaudoElaborado == POR_TERCEIROS && !laudoAvaliacao.isNaoHomologacao\"><responsavel-tecnico incluir-usuario-logado=false lista-responsaveis-tecnicos=laudoAvaliacao.homologadores custom-title=\"\'label-responsaveis-nota-tecnica-homologacao\'\" assinatura-nota-tecnica=true assinatura-laudo-relatorio=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == PREENCHER_SISTEMA\" detalhar-laudo-relatorio=detalharLaudoRelatorio></responsavel-tecnico><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-nota-tecnica-homologacao\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=laudoAvaliacao.tipoNotaTecnicaHomologacaoTemp ng-change=limpartipoNotaTecnicaHomologacao() name=\"{{nomeCampo}} - {{\'label-utilizar-laudo-preenchido\' | translate}}\" ng-disabled=detalharLaudoRelatorio><md-radio-button ng-repeat=\"(k,v) in listaTipoNotaTecnicaHomologacao\" ng-if=\"k != \'DOCUMENTO_SEI\'\" flex=33 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary m-r-20\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></md-radio-group></div><div ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == PREENCHER_SISTEMA\"><md-button style=\"font-weight:normal; text-transform:none\" class=\"md-primary md-raised no-margin\" ng-click=inserirNotaTecnicaHomologacao() ng-if=!laudoAvaliacao.notaTecnicaHomologacao><label>{{\'label-preencher-alterar-nota\' | translate}}</label></md-button><div layout=row layout-align=\"center start\" ng-if=\"laudoAvaliacao.notaTecnicaHomologacao && !detalharLaudoRelatorio\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-nome\' | translate}}</th><th md-column>{{\'label-situacao\' | translate}}</th><th md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><a herf=\"\">{{\'label-preencher-alterar-nota\' | translate}}</a></td><td md-cell ng-style=\"laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento ? {} : labelVermelho\"><label ng-bind=\"laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento ? (\'label-preenchida-em\' | translate) + \' \' + (laudoAvaliacao.notaTecnicaHomologacao.dataPreenchimento | date: \'dd/MM/yyyy\' : \'-0300\') : (\'label-preenchimento-incompleto\' | translate)\"></label></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=inserirNotaTecnicaHomologacao() class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-editar\' | translate}}\" ng-bind=\"\'edit\'\" aria-label=\"{{\'label-editar\' | translate}}\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=limparNotaTecnica() class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir\' | translate}}\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container></div></div><div layout=row layout-align=\"center start\" ng-if=\"laudoAvaliacao.notaTecnicaHomologacao && detalharLaudoRelatorio\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-nome\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><a ng-show=\"laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_CHEFIA\' && laudoAvaliacao.situacaoLaudo != \'EM_PROCESSO_HOMOLOGACAO\' && laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_TECNICO\' && laudoAvaliacao.situacaoLaudo != \'PENDENTE_HOMOLOGACAO\' && laudoAvaliacao.situacaoLaudo != \'EM_PREENCHIMENTO\'\" class=mouse-pointer ng-href={{downloadNotaTecnicaHomologacao()}} target=_blank>Nota Técnica de Homologação</a> <a ng-show=\"laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_CHEFIA\' || laudoAvaliacao.situacaoLaudo == \'EM_PROCESSO_HOMOLOGACAO\' || laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_TECNICO\' || laudoAvaliacao.situacaoLaudo == \'PENDENTE_HOMOLOGACAO\' || laudoAvaliacao.situacaoLaudo == \'EM_PREENCHIMENTO\'\" class=mouse-pointer href=\"\" ng-click=visualizarDocumentoNotaTecnica() target=_blank>Nota Técnica de Homologação</a></td></tr></tbody></table></md-table-container></div></div></div><documento-laudo-avaliacao-padrao ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == UPLOAD_ARQUIVO\" lista-documentos-laudo=laudoAvaliacao.arquivosNotaTecnicaHomologacao lista-documentos-aux=laudoAvaliacao.arquivosLaudoReferencia campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_01\'\" titulo=\"\'Nota Técnica de Homologação\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-avaliacao-padrao><div layout=row layout-xs=column flex-sm=column ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == UPLOAD_ARQUIVO\"><md-input class=md-block flex=100 flex-xs=100 flex-sm=100><div layout=row><label>{{\'label-parecer-tecnico\' | translate}}:*</label></div><md-radio-group class=tamanho-radio-button-avaliacao ng-model=laudoAvaliacao.homologacao required ng-disabled=detalharLaudoRelatorio><div layout=row class=m-t-20><md-radio-button layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=true><div class=texto-preto>Sugiro pela <b>homologação</b> do laudo de avaliação</div></md-radio-button></div><div layout=row><md-radio-button layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=false><div class=texto-preto>Sugiro pela <b>não homologação</b> do laudo de avaliação</div></md-radio-button></div></md-radio-group></md-input></div></div><documento-laudo-avaliacao-padrao lista-documentos-laudo=laudoAvaliacao.arquivosLaudoReferencia lista-documentos-aux=laudoAvaliacao.arquivosNotaTecnicaHomologacao detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-validacao=\"\'CONTENT_TYPE_02\'\" titulo=\"\'label-laudo-avaliacao\' | translate\"></documento-laudo-avaliacao-padrao></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div class=m-b-20 layout=row flex=100 ng-show=\"!detalharLaudoRelatorio && laudoAvaliacao.tipoLaudoElaborado == \'PELO_ORGAO_ENTIDADE\'\"><div flex=100 layout=column><md-checkbox class=md-primary ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.termoResponsabilidade.isAssinado name=\"{{\'label-termo-responsabilidade\' | translate}}\" ng-required=\"laudoAvaliacao.tipoLaudoElaborado == \'PELO_ORGAO_ENTIDADE\'\"><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_AVALIACAO_LOCACAO\">{{\'Eu, \' + usuarioLogado.nome + \', CPF: \' + (usuarioLogado.cpf | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-laudo-avaliacao-locacao\' | translate}}</label><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_RELATORIO_VALOR_REFERENCIA\">{{\'Eu, \' + usuarioLogado.nome + \', CPF: \' + (usuarioLogado.cpf | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-relatorio-valor-referencia\' | translate}}</label></md-checkbox></div></div><div class=m-b-20 layout=row flex=100 ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.tipoLaudoElaborado == \'PELO_ORGAO_ENTIDADE\'\"><div flex=100 layout=column><md-checkbox class=md-primary ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.termoResponsabilidade.isAssinado name=\"{{\'label-termo-responsabilidade\' | translate}}\" ng-required=\"laudoAvaliacao.tipoLaudoElaborado == \'PELO_ORGAO_ENTIDADE\'\"><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_AVALIACAO_LOCACAO\">{{\'Eu, \' + laudoAvaliacao.termoResponsabilidade.nomeAssinador + \', CPF: \' + (laudoAvaliacao.termoResponsabilidade.cpfAssinador | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-laudo-avaliacao-locacao\' | translate}}</label><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_RELATORIO_VALOR_REFERENCIA\">{{\'Eu, \' + laudoAvaliacao.termoResponsabilidade.nomeAssinador + \', CPF: \' + (laudoAvaliacao.termoResponsabilidade.cpfAssinador | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-relatorio-valor-referencia\' | translate}}</label></md-checkbox></div></div><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarComoRascunhoLaudoAvaliacao() ng-if=!detalharLaudoRelatorio><label>{{\'button-salvar-rascunho\' | translate}}</label></md-button><md-button ng-if=\"!detalharLaudoRelatorio && laudoAvaliacao.tipoNotaTecnicaHomologacao == PREENCHER_SISTEMA && verificarHomologador()\" class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarLaudoAvaliacao(formularioDadosLaudo)><label>{{\'button-gravar-assinar-nota-tecnica\' | translate}}</label></md-button><md-button ng-if=\"!detalharLaudoRelatorio && laudoAvaliacao.tipoNotaTecnicaHomologacao != PREENCHER_SISTEMA || (!detalharLaudoRelatorio && !verificarHomologador())\" class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarLaudoAvaliacao(formularioDadosLaudo)><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/laudo-avaliacao/views/cadastrarLaudoRelatorio.html","<form name=formularioDadosLaudo novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina ng-show=!detalharLaudoRelatorio>{{\'title-inserir-dados-relatorio\' | translate}}</h2><h2 class=titulo-pagina ng-show=detalharLaudoRelatorio>{{\'title-detalhar-relatorio-valor-referencia\' | translate}}</h2></div></div><!-- Dados do imóvel --><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-rip\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column class=m-b-20><md-input-container class=md-block flex=50><div><a href=\"\" ng-click=\"ripImovelLink(imovel.rip || rip);\" target=_blank class=rip-link>{{ imovel.rip || rip }}</a></div></md-input-container></div><div ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.dataCancelamento\" layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar>{{\'title-dados-cancelamento\' | translate}}<md-input-container ng-if=\"laudoAvaliacao.situacaoLaudo==\'PENDENTE_VALIDACAO_CHEFIA\'\" flex=4><b style=\"margin-left:20px;font-size: 14px;color: #d41515 !important\">{{\'title-pendencia-validacao\' | translate}}</b></md-input-container></h3></div></div><div layout=row layout-xs=column ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.dataCancelamento\"><md-container flex=50 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row flex=100><md-datepicker ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.dataCancelamento name=\"{{nomeCampo}} - {{\'label-data\' | translate}}\" md-placeholder=\"{{\'placeholder-data\' | translate}}\" class=datePickerClass></md-datepicker></div></md-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-responsavel\' | translate}}:</label><input type=text ng-model=laudoAvaliacao.responsavelCancelamento ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=row layout-xs=column ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.dataCancelamento\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-motivo-cancelamento\' | translate}}:</label><input type=text ng-model=listaMotivosCancelamento[laudoAvaliacao.motivoCancelamento] ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=row layout-xs=column ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.dataCancelamento\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-justificativa\' | translate}}</label><textarea rows=4 cols=50 ng-model=laudoAvaliacao.justificativaCancelamento ng-disabled=detalharLaudoRelatorio></textarea></md-input-container></div><detalhar-revalidacao ng-if=\"detalharLaudoRelatorio && verificarRevalidacoes(laudoAvaliacao)\" laudo-avaliacao=laudoAvaliacao></detalhar-revalidacao><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar>{{\'title-dados-relatorio\' | translate}}</h3></div></div><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-tipo-avaliacao\' | translate}}:*</span></span></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{labelTipoLaudo()}}:*</span></span></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50><div><b><span>{{listaTiposAvaliacao[laudoAvaliacao.tipoAvaliacao]}}</span></b></div></md-input-container><md-input-container class=md-block flex=50><md-select ng-model=laudoAvaliacao.tipoLaudoElaborado name=\"{{nomeCampo}} - Tipo Laudo Elaborado\" required ng-disabled=\"detalharLaudoRelatorio || !laudoAvaliacao.tipoAvaliacao\" aria-label=\"Tipo Laudo Elaborado\"><md-option ng-repeat=\"(k,v) in listaElaboracaoLaudo\" ng-value=k ng-if=\"detalharLaudoRelatorio || k != \'CADASTRADO_SISTEMA\'\">{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column ng-if=detalharLaudoRelatorio><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-prazo-validade\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column class=m-b-20 ng-if=detalharLaudoRelatorio><md-input-container class=md-block flex=50><div><b><span>{{laudoAvaliacao.inicioPeriodo | date: \'dd/MM/yyyy\' : \'-0300\'}} a {{laudoAvaliacao.finalPeriodo | date: \'dd/MM/yyyy\' : \'-0300\'}}</span></b></div></md-input-container></div><div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 ng-if=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_RELATORIO_VALOR_REFERENCIA\'\"><label>{{\'label-tipo-metodologia\' | translate}}:</label><md-select id=tipoMetodologia ng-model=laudoAvaliacao.tipoMetodologia name=\"{{nomeCampo}} - Metodologia\" ng-required=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_RELATORIO_VALOR_REFERENCIA\'\" ng-change=\"laudoAvaliacao.metodologiaOutro = null\" ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"(k,v) in listaTiposMetodologia\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=100 ng-if=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_AVALIACAO\'\"><label>{{\'label-tipo-metodo-avaliacao\' | translate}}:</label><md-select id=tipoMetodosAvaliacao ng-model=laudoAvaliacao.tipoMetodoAvaliacao name=\"{{nomeCampo}} - Método de avaliação\" ng-required=\"laudoAvaliacao.tipoAvaliacao == \'LAUDO_AVALIACAO\'\" ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"(k,v) in listaTiposMetodosAvaliacao\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column ng-show=\"laudoAvaliacao.tipoMetodologia == \'OUTRO\'\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-descricao-outra-metodologia\' | translate}}:</label><input type=text ng-model=laudoAvaliacao.metodologiaOutro ng-required=\"laudoAvaliacao.tipoMetodologia == \'OUTRO\'\" ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=column layout-xs=column ng-show=\"laudoAvaliacao.tipoLaudoElaborado != \'CADASTRADO_SISTEMA\'\"><md-input-container class=md-block flex=100><label>{{\'label-unidade\' | translate}}:</label><md-select id=unidade ng-model=laudoAvaliacao.unidade name=\"{{\'label-unidade\' | translate}}\" ng-required=true ng-disabled=\"detalharLaudoRelatorio || listaUnidades.length == 1\"><md-option ng-repeat=\"item in listaUnidades\" ng-value=item>{{item}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-subunidade\' | translate}}</label><input type=text maxlength=150 ng-model=laudoAvaliacao.subunidade ng-disabled=detalharLaudoRelatorio></md-input-container></div><fieldset ng-init=\"laudoAvaliacao.tipoArquivo=\'UPLOAD_ARQUIVO\'\" class=fieldsetArredondado layout=column layout-xs=column ng-show=\"laudoAvaliacao.tipoLaudoElaborado != \'CADASTRADO_SISTEMA\'\"><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'label-relatorio-valor-referencia\' | translate}}{{ !(detalharLaudoRelatorio && laudoAvaliacao.tipoArquivo == DOCUMENTO_SEI) && (\' – \' + (\'label-upload-arquivo\' | translate)) || \'\'}}</h3></div><div layout=column flex ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.tipoArquivo == DOCUMENTO_SEI\"><label class=sidenavTitulos>{{\'label-selecione\' | translate}}*</label><md-radio-group class=m-t-10 flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=laudoAvaliacao.tipoArquivo ng-disabled=detalharLaudoRelatorio ng-change=limparArquivosLaudoReferencia() ng-required=true><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=10 ng-value=UPLOAD_ARQUIVO><div class=texto-preto>{{\'label-upload-arquivo\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=15 ng-value=DOCUMENTO_SEI><div class=texto-preto>{{\'label-utilizar-documento-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><documento-sei ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.tipoArquivo == DOCUMENTO_SEI\" detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=laudoAvaliacao.arquivosLaudoReferencia titulo=\"\'label-incluir-documentos\' | translate\" uf=imovel.enderecamento.uf></documento-sei><div ng-if=\"laudoAvaliacao.tipoArquivo == \'UPLOAD_ARQUIVO\'\"><documento-laudo-avaliacao-padrao lista-documentos-laudo=laudoAvaliacao.arquivosLaudoReferencia lista-documentos-aux=laudoAvaliacao.arquivosNotaTecnicaHomologacao detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-validacao=\"\'CONTENT_TYPE_04\'\"></documento-laudo-avaliacao-padrao><div ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.tipoArquivo == DOCUMENTO_SEI\"><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=laudoAvaliacao.laudoAvaliacaoDocumentoSei.isEnviarDocumento ng-change=changeLaudoAvaliacaoDocumentoSei() ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-enviar-documento-para-sei\' | translate}}</label></md-checkbox></div><div ng-if=laudoAvaliacao.laudoAvaliacaoDocumentoSei.isEnviarDocumento layout=column flex><md-radio-group flex class=\"m-l-30 tamanho-radio-button-avaliacao md-block\" ng-model=laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento ng-disabled=\"detalharLaudoRelatorio || !laudoAvaliacao.laudoAvaliacaoDocumentoSei.isEnviarDocumento\" ng-change=changeLaudoAvaliacaoNumeroSei() ng-required=laudoAvaliacao.laudoAvaliacaoDocumentoSei.isEnviarDocumento><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'CRIAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-criar-novo-processo-sei\' | translate}}</div></md-radio-button></div><div class=\"m-b-10 m-l-60\" layout=row ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento == \'CRIAR_PROCESSO_SEI\' && laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei\"><div class=texto-preto>{{\'label-n-processo\' | translate}}{{\': \' + laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei | processoSei}}</div></div><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'UTILIZAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-utilizar-processo-existente-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><md-input-container ng-if=\"laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" class=\"md-block m-l-60\" flex=30 flex-xs=100 flex-sm=100><label>{{\'label-numero-processo-sei\' | translate}}</label><input type=text ui-mask=99999.999999/9999-99 ng-required=\"laudoAvaliacao.laudoAvaliacaoDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" ng-model=laudoAvaliacao.laudoAvaliacaoDocumentoSei.numeroProcessoSei ng-disabled=detalharLaudoRelatorio ng-blur=validarLaudoAvaliacaoNumeroSeiProcessoSei()></md-input-container></div></div><div ng-if=\"detalharLaudoRelatorio && laudoAvaliacao.tipoArquivo == DOCUMENTO_SEI\"><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=laudoAvaliacao.isArquivoModelo ng-change=limparDadosArquivosModelo(); name=\"{{\'label-incluir-arquivo-modelo\' | translate}}\" ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-incluir-arquivo-modelo\' | translate}}</label><md-tooltip md-direction=top>Adicionar arquivo do software utilizado para o cálculo</md-tooltip></md-checkbox></div><div layout=column flex ng-if=laudoAvaliacao.isArquivoModelo><documento-laudo-avaliacao-padrao hint=\"\'Adicionar arquivo do software utilizado para o cálculo\'\" titulo=\"\'Arquivos do Modelo\'\" lista-documentos-laudo=laudoAvaliacao.arquivosModelo campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_03\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-avaliacao-padrao></div></div></fieldset><div ng-if=\"laudoAvaliacao.tipoLaudoElaborado != \'CADASTRADO_SISTEMA\'\"><responsavel-tecnico incluir-usuario-logado=false lista-responsaveis-tecnicos=laudoAvaliacao.responsaveis detalhar-laudo-relatorio=detalharLaudoRelatorio assinatura-nota-tecnica=false assinatura-laudo-relatorio=false descricao-hint=\"\'Autor do Relatório Valor de Referência\'\"></responsavel-tecnico></div><div layout=\"row m-t-10\" layout-xs=column><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-data-de-assinatura\' | translate}}:*</span></span></div></div><div layout=row layout-xs=column><md-container flex=\"{{imovel.enderecamento.pais != BRASIL && !detalharLaudoRelatorio ? \'33\' : \'50\'}}\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row flex=100><md-datepicker ng-required=!(detalharLaudoRelatorio) ng-if=!detalharLaudoRelatorio ng-model=laudoAvaliacao.dataAssinaturaTemp name=\"{{nomeCampo}} - {{\'label-data-de-assinatura\' | translate}}\" md-placeholder=\"{{\'placeholder-data-assinatura\' | translate}}*\" class=datePickerClass ng-change=changeValoresDataValor();></md-datepicker><span ng-if=detalharLaudoRelatorio class=\"m-t-10 texto-preto\">{{laudoAvaliacao.dataAssinaturaTemp | date: \'dd/MM/yyyy\' : \'-0300\'}}</span></div></md-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 ng-if=\"imovel.enderecamento.pais != BRASIL && !detalharLaudoRelatorio\"><label>{{\'label-moeda\' | translate}}:</label><md-select id=moeda ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.moeda name=\"{{nomeCampo}} - Moeda\"><md-option ng-value=moeda ng-repeat=\"moeda in listaMoedas\">{{moeda.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=\"{{imovel.enderecamento.pais != BRASIL && !detalharLaudoRelatorio ? \'33\' : \'50\'}}\" flex-xs=100 flex-sm=100><div layout=row><label>{{\'label-valor-imovel\' | translate}} {{laudoAvaliacao.moeda ? laudoAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}:</label></div><input type=value ng-model=laudoAvaliacao.valorImovelTemp ng-blur=changeValoresDataValor() name=\"{{nomeCampo}} - {{\'label-valor-imovel\' | translate}}\" ui-number-mask=2 maxlength=20 ng-required=!(detalharLaudoRelatorio) ng-disabled=detalharLaudoRelatorio></md-input-container></div><div ng-show=laudoAvaliacao.dataAssinatura><div layout=row layout-xs=column ng-if=exibirInserirValoresTerrenoBenfeitoria()><div flex><h3 class=subtitulo-pagina-detalhar>{{\'valores-terreno-benfeitoria\' | translate}}</h3></div></div><div layout=row layout-xs=column ng-if=exibirInserirValoresTerrenoBenfeitoria()><div flex><inserir-valores-terreno-benfeitoria lista-benfeitorias-imovel=laudoAvaliacao.benfeitoriasImovel moeda=laudoAvaliacao.moeda unidade-autonoma-imovel=laudoAvaliacao.unidadeAutonomaIncorporacao tipo-valor-laudo=laudoAvaliacao.tipoValorLaudo?laudoAvaliacao.tipoValorLaudo:INFORMAR_VALORES_LAUDO tipo-laudo=LAUDO_AVALIACAO detalhar-laudo-relatorio=\"detalharLaudoRelatorio || laudoAvaliacao.laudoRelatorioAvaliacao\" redirecionar-informar-valor-cub=redirecionarInformarValorCub validar-valor-imovel=validarValorImovel total-valor-terreno=laudoAvaliacao.valorTotalTerreno valor-adotado-selecionado=laudoAvaliacao.valorImovel valor-terreno=laudoAvaliacao.valorTerreno unidade-medida=unidadeMedida lista-moedas=listaMoedas pais-imovel=imovel.enderecamento.pais termo-responsabilidade=laudoAvaliacao.termoResponsabilidade></inserir-valores-terreno-benfeitoria></div></div><!--             <div ng-if=\"(laudoAvaliacao.tipoValorLaudo == ESTIMAR_VALORES) && false\">\n                <md-input layout=\"column\" class=\"md-block\" flex=\"100\">\n                    <label>{{\'label-fonte-valor-terreno\' | translate}}:</label>\n                    <md-radio-group class=\"tamanho-radio-button-avaliacao\" ng-model=\"fonteValorTerreno\"\n                                    name=\"{{nomeCampo}} - {{\'label-fonte-valor-terreno\' | translate}}\" ng-disabled=\"detalharLaudoRelatorio\">\n                        <div layout=\"row\">\n                            <md-radio-button ng-repeat=\"(k,v) in listaFonteValorTerreno\" layout-align=\"space-between\" class=\"tamanho-radio-button-avaliacao md-primary\"\n                                             flex=\"20\" ng-value=\"k\" ng-if=\"k != REFERENCIA_INCRA || laudoAvaliacao.laudoRelatorioAvaliacao.detalhesImovel.imovel.naturezaImovel == RURAL\">\n                                <div class=\"texto-preto\">{{v}}</div>\n                            </md-radio-button>\n                        </div>\n                    </md-radio-group>\n                </md-input>\n                <br>\n                <md-input layout=\"row\" class=\"md-block\">\n                    {{\'label-valor-terreno-rs\' | translate}}: {{laudoAvaliacao.valorTerreno | currency}}\n                </md-input>\n                <br>\n                <md-input layout=\"row\" class=\"md-block\">\n                    {{\'label-valor-m2\' | translate}}: {{laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.valorTerreno && laudoAvaliacao.areaTerreno ? laudoAvaliacao.laudoRelatorioAvaliacao.avaliacao.valorTerreno / laudoAvaliacao.areaTerreno : \'\' | currency}}\n                </md-input>\n                <br>\n                <md-input layout=\"row\" class=\"md-block\">\n                    {{\'label-periodo-avaliacao\' | translate}}: {{laudoAvaliacao.laudoRelatorioAvaliacao.periodoInicial | date: \'dd/MM/yyyy\' : \'-0300\'}} até {{laudoAvaliacao.laudoRelatorioAvaliacao.periodoFinal | date: \'dd/MM/yyyy\' : \'-0300\'}}\n                </md-input>\n                <br>\n                <div layout=\"row\" flex=\"100\">\n                    <md-input-container layout=\"row\" class=\"md-block\" flex=\"50\">\n                        <label>{{\'label-valor-terreno\' | translate}}:</label>\n                        <input type=\"value\" ng-model=\"valorTerreno\" name=\"{{nomeCampo}} - {{\'label-valor-terreno\' | translate}}\" ui-number-mask=\"2\"\n                               ng-disabled=\"detalharLaudoRelatorio\"/>\n                    </md-input-container>\n                    <md-input-container flex=\"50\">{{valorTerreno ? convertNumeroPorExtenso(valorTerreno) : null}}</md-input-container>\n                </div>\n                <md-input-container layout=\"row\" class=\"md-block\">\n                    <label>{{\'label-fonte\' | translate}}:</label>\n                    <textarea class=\"barraRolagemTextArea\" ng-model=\"avaliacao.pesquisaMercado\" ui-mask-placeholder-char=\"space\"\n                              name=\"{{nomeCampo}} - {{\'label-fonte\' | translate}}\" ng-disabled=\"detalharLaudoRelatorio\"></textarea>\n                </md-input-container>\n            </div> --></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div class=m-b-20 layout=row flex=100 ng-show=!detalharLaudoRelatorio><div flex=100 layout=column><md-checkbox class=md-primary ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.termoResponsabilidade.isAssinado name=\"{{\'label-termo-responsabilidade\' | translate}}\" ng-required=true aria-label=\"Assinatura do termo de responsabilidade\"><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_AVALIACAO\">{{\'Eu, \' + usuarioLogado.nome + \', CPF: \' + (usuarioLogado.cpf | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-laudo-avaliacao\' | translate}}</label><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_RELATORIO_VALOR_REFERENCIA\">{{\'Eu, \' + usuarioLogado.nome + \', CPF: \' + (usuarioLogado.cpf | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-relatorio-valor-referencia\' | translate}}</label></md-checkbox></div></div><!-- Enquanto o laudo estiver na situação \"Em Preenchimento\" não é possível exibir os dados do aceite, pois o atributo \"termoResponsabilidade\" é nulo --><div class=m-b-20 layout=row flex=100 ng-show=\"detalharLaudoRelatorio && laudoAvaliacao.termoResponsabilidade !== null && laudoAvaliacao.situacaoLaudo !== \'EM_PREENCHIMENTO\'\"><div flex=100 layout=column><md-checkbox class=md-primary ng-disabled=detalharLaudoRelatorio ng-model=laudoAvaliacao.termoResponsabilidade.isAssinado name=\"{{\'label-termo-responsabilidade\' | translate}}\" aria-label=\"Assinatura do termo de responsabilidade\"><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_AVALIACAO\">{{\'Eu, \' + laudoAvaliacao.termoResponsabilidade.nomeAssinador + \', CPF: \' + (laudoAvaliacao.termoResponsabilidade.cpfAssinador | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-laudo-avaliacao\' | translate}}</label><label class=text-black ng-show=\"laudoAvaliacao.tipoAvaliacao == LAUDO_RELATORIO_VALOR_REFERENCIA\">{{\'Eu, \' + laudoAvaliacao.termoResponsabilidade.nomeAssinador + \', CPF: \' + (laudoAvaliacao.termoResponsabilidade.cpfAssinador | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-relatorio-valor-referencia\' | translate}}</label></md-checkbox></div></div><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarComoRascunhoLaudoAvaliacao() ng-if=!detalharLaudoRelatorio><label>{{\'button-salvar-rascunho\' | translate}}</label></md-button><md-button ng-if=\"!detalharLaudoRelatorio && laudoAvaliacao.tipoNotaTecnicaHomologacao == PREENCHER_SISTEMA && verificarHomologador()\" class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarLaudoAvaliacao(formularioDadosLaudo)><label>{{\'button-gravar-assinar-nota-tecnica\' | translate}}</label></md-button><md-button ng-if=\"!detalharLaudoRelatorio && laudoAvaliacao.tipoNotaTecnicaHomologacao != PREENCHER_SISTEMA || (!detalharLaudoRelatorio && !verificarHomologador())\" class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarLaudoAvaliacao(formularioDadosLaudo)><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/laudo-avaliacao/views/cancelarLaudoAvaliacao.html","<form name=formularioCancelarPvg novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{titulo}}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-data\' | translate}}:</label><input type=text ng-disabled=true ng-model=dataMostrar></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-nome-responsavel\' | translate}}:</label><input type=text ng-disabled=true ng-model=nomeResponsavel.responsavelCancelamento></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100><label>{{\'label-motivo\' | translate}}:</label><md-select id=motivoCancelamento ng-model=selecioneMotivo name=\"Motivo Cancelamento\" required><md-option ng-repeat=\"(k,v) in motivosCancelar\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div class=m-b-30 layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-offset=5><label>{{\'label-justificativa\' | translate}}</label><textarea rows=4 cols=50 ng-model=justificativa maxlength=500 required></textarea><span style=\"color: #cc0000; float: right\">{{0 + justificativa.length}} / 500</span></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=cancelar(formularioCancelarPvg);><label>{{\'button-confirmar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/laudo-relatorio-avaliacao/language/pt-BR.json","{ \"button-salvar-rascunho\":\"Salvar Rascunho\", \"button-avancar\":\"Avançar\", \"button-concluir\": \"Concluir\", \"button-gerar-pdf\": \"Gerar PDF\", \"button-concluir-assinar\": \"Concluir e Assinar\", \"label-consultar-laudo\": \"Consultar Laudo/Relatório\", \"label-tipo-laudo\": \"Tipo\", \"label-metodo-avaliacao\": \"Método de Avaliação\", \"label-situacao-laudo\": \"Situação\", \"label-periodo-cadastramento\": \"Período de Cadastramento do Laudo/Relatório\", \"label-numero-laudo\": \"Nº do Laudo/Relatório\", \"label-validade\": \"Validade\", \"label-rip\": \"RIP\", \"label-tipo-imovel\": \"Tipo do Imóvel\", \"label-natureza-imovel\": \"Natureza do Imóvel\", \"label-pais\": \"País\", \"label-uf\": \"UF\", \"label-cep\": \"CEP\", \"label-municipio\": \"Município\", \"label-bairro\": \"Bairro/Logradouro\", \"label-proprietario\": \"Proprietário/Titular do Imóvel\", \"label-responsavel-tecnico\": \"Responsável Técnico\", \"label-laudo-relatorio\": \"Laudo/Relatório de Avaliação\", \"label-cpf\": \"CPF\", \"label-crea\": \"CREA\", \"label-crea-cau\": \"CREA/CAU\", \"label-solicitante\": \"Solicitante\", \"label-objetivo\": \"Objetivo\", \"label-finalidade\": \"Finalidade\", \"label-tipo\": \"Tipo\", \"label-tratamento\":\"Tratamento\", \"label-ferramenta-analitica\":\"Ferramenta Analítica\", \"label-normas-utilizadas\":\"Normas Utilizadas\", \"label-nao-sei-rip\": \"Não sei o RIP\", \"label-tipo-logradouro\" : \"Tipo Logradouro\", \"label-numero\":\"Nº\", \"label-bairro-distrito\":\"Bairro/Distrito\", \"label-complemento\":\"Complemento\", \"label-codigo-postal\":\"Código Postal\", \"label-endereco\":\"Endereço\", \"title-laudo-relatorio-avaliacao\": \"Laudo/Relatório de Avaliação\", \"title-motivo\": \"Motivo\", \"tab-relatorio-avaliacao-evolutivo\": \"RESULTADO DA AVALIAÇÃO\", \"tab-identicacao\": \"IDENTIFICAÇÃO\", \"tab-detalhes-imovel\": \"DETAlHES DO IMÓVEL\", \"tab-terreno-imovel\": \"TERRENO DO IMÓVEL\", \"tab-benfeitorias\": \"BENFEITORIAS\", \"tab-unidade-autonoma\": \"UNIDADE AUTÔNOMA\", \"tab-utilizacao\": \"UTILIZAÇÃO\", \"tab-vistoria\": \"VISTORIA\", \"tab-avaliacao\": \"AVALIAÇÃO\", \"tab-avaliacao-terreno\": \"AVALIAÇÃO TERRENO\", \"tab-cancelamento\": \"CANCELAMENTO\", \"tab-resultado-avaliacao\": \"RESULTADO DA AVALIAÇÃO\", \"tab-resultados-avaliacao\": \"RESULTADOS DA AVALIAÇÃO\", \"tab-resultados-avaliacao-terreno\": \"RESULTADOS DA AVALIAÇÃO DO TERRENO\", \"tab-quantificacao-custo\": \"Quantificação de custo\", \"mensagem-deseja-gravar-dados-rascunho-confirm\": \"Deseja gravar os dados como rascunho?\", \"mensagem-deseja-gravar-dados-rascunho-confirm-adicionar-variavel\": \"É necessario salvar o dados como rascunho para adicionar uma varíavel. Deseja gravar os dados como rascunho?\", \"mensagem-deseja-gravar-dados-rascunho-confirm-adicionar-variavel-nao\": \"Não é possivel adicionar uma variável sem salvar o laudo como rascunho.\", \"mensagem-metodo-avaliacao-invalido\": \"O método de avaliação selecionado não é permitido para imóveis que não possuem benfeitorias.\", \"pdf-laudo-de-avaliacao\": \"Laudo de avaliação\", \"pdf-rip\": \"RIP\", \"pdf-finalidade\": \"Finalidade\", \"pdf-solicitante\": \"Solicitante\", \"pdf-objetivo\": \"Objetivo\", \"pdf-normas-utilizadas\": \"Normas Utilizadas\", \"pdf-metodo-de-avaliacao\": \"Método de Avaliação\", \"pdf-tratamento\": \"Tratamento\", \"pdf-ferramenta-analitica\": \"Ferramenta Analítica\", \"pdf-responsavel-tecnico\": \"Responsável(is) Técnico(s)\", \"pdf-cpf\": \"CPF\", \"pdf-nome\": \"Nome\", \"pdf-crea-cau\": \"CREA/CAU\", \"pdf-data\": \"Data\", \"pdf-resultados-avaliacao\": \"Resultados da Avaliação\", \"pdf-enquadramento-fundamentacao\": \"Enquadramento quanto a grau de fundamentação\", \"pdf-enquadramento-precisao\": \"Enquadramento quanto a grau de precisão\", \"pdf-valor-adotado\": \"Valor Adotado\", \"pdf-valor-minimo\": \"Valor Mínimo\", \"pdf-valor-medio\": \"Valor Médio\", \"pdf-valor-maximo\": \"Valor Máximo\", \"pdf-liquidez-do-imovel\": \"Liquidez do Imóvel\", \"pdf-sumario\": \"Sumário\", \"pdf-identificacao-imovel\": \"Identificação e caracterização do imóvel\", \"pdf-localizacao-imovel\": \"Localização do imóvel\", \"pdf-detalhes-imovel\": \"Detalhes do imóvel\", \"pdf-dados-cartoriais\": \"Dados cartoriais\", \"pdf-proprietario-imovel\": \"Proprietário do imóvel\", \"pdf-descrição-regiao\": \"Descrição da região\", \"pdf-descricao-benfeitoria\": \"Descrição da benfeitoria\", \"pdf-utilizacao\": \"Utilização\", \"pdf-dados-utilizacao\": \"Dados da Utilização\", \"pdf-caracterizacao-terreno\": \"Caracterização do terreno\", \"pdf-area-terreno\": \"Área do Terreno\", \"pdf-area\": \"Área\", \"pdf-descricao-carateristicas-fisicas\": \"Descrição das Características físicas do terreno\", \"pdf-parametros-urbanisticos\": \"Parâmetros urbanísticos\", \"pdf-benfeitorias\": \"Benfeitoria(s)\", \"pdf-benfeirotia-e3\": \"Benfeitoria E3 - Edificação/Edifício Multipavimentos\", \"pdf-benfeitoria-c1\": \"Benfeitoria C1 - Complementar\", \"pdf-unidade-autonoma\": \"Unidade Autônoma\", \"pdf-dados-edificacao\": \"Dados da Edificação\", \"pdf-descricao-unidade-autonoma\": \"Descrição da Unidade Autônoma\", \"pdf-avaliacao\": \"Avaliação\", \"pdf-dados-avaliacao\": \"Dados da Avaliação\", \"pdf-resultados\": \"Resultados\", \"pdf-enquadramento-laudo-fundamentacao\": \"Enquadramento do Laudo segundo grau de fundamentação - NBR 14653-2\", \"pdf-enquadramento-laudo-precisao\": \"Enquadramento do Laudo segundo grau de precisão - NBR 14653-2\", \"pdf-estimativa-parcelas\": \"Estimativa das parcelas de terreno e benfeitoria\", \"pdf-assinaturas\": \"Assinaturas\", \"pdf-anexos\": \"Anexos\", \"pdf-pais\": \"País\", \"pdf-uf\": \"UF\", \"pdf-municipio\": \"Município\", \"pdf-endereco\": \"Endereço\", \"pdf-numero\": \"Número\", \"pdf-cep\": \"CEP\", \"pdf-codigo-postal\": \"Código Postal\", \"pdf-tipo-imovel\": \"Tipo de imóvel\", \"pdf-natureza-imovel\": \"Natureza do imóvel\", \"pdf-observacoes-natureza\": \"Observações da natureza do imóvel\", \"pdf-observacoes-gerais\": \"Observações gerais\", \"pdf-localizacao\": \"Localização\", \"pdf-cartorio\": \"Cartório\", \"pdf-matricula-transcricao\": \"Matrícula/Transcrição\", \"pdf-data-matricula\": \"Data da Matrícula\", \"pdf-data-registro\": \"Data do Registro\", \"pdf-tipo-proprietario\": \"Tipo de proprietário\", \"pdf-orgao-entidade\": \"Órgão/Entidade\", \"pdf-ug-especifica\": \"UG Especifica\", \"pdf-cpf-cnpj\": \"CPF/CNPJ\", \"pdf-nome-razao-social\": \"Nome/Razão Social\", \"pdf-infraestrutura-urbana\": \"Infraestrutura urbana\", \"pdf-pavimentacao\": \"Pavimentação\", \"pdf-iluminacao\": \"Iluminação\", \"pdf-sarjeta-guia\": \"Sarjeta/guia\", \"pdf-rede-agua\": \"Rede de Água\", \"pdf-rede-eletrica\": \"Rede Elétrica\", \"pdf-passeio\": \"Passeio\", \"pdf-rede-esgoto\": \"Rede de esgoto\", \"pdf-coleta-de-lixo\": \"Coleta de lixo\", \"pdf-observacoes-infra-urbana\": \"Observações da Infraestrutura urbana\", \"pdf-descricao-servicos-comunitarios\": \"Descrição de outros serviços comunitários\", \"pdf-servicos-comunitarios\": \"Serviços comunitários\", \"pdf-instrumento-destinacao\": \"Instrumento de Destinação\", \"pdf-tipo-uso\": \"Tipo de Uso\", \"pdf-especificacao\": \"Especificação\", \"pdf-descricao\": \"Descrição\", \"pdf-area-total\": \"Área Total\", \"pdf-area-uniao\": \"Área da união\", \"pdf-fracao-imovel\": \"Fração ideal do Imóvel\", \"pdf-fracao-uniao\": \"Fração ideal da União\", \"pdf-dimensoes\": \"Dimensões\", \"pdf-nivel-rua\": \"Nível da rua\", \"pdf-formato\": \"Formato\", \"pdf-topografia\": \"Topografia\", \"pdf-observacoes\": \"Observações\", \"pdf-afastamento-lateral\": \"Afastamento Lateral\", \"pdf-afastamento-frontal\": \"Afastamento Frontal\", \"pdf-gabarito\": \"Gabarito\", \"pdf-taxa-ocupacao-max\": \"Taxa de Ocupação Máxima\", \"pdf-indice-aproveitamento\": \"Índice de Aproveitamento\", \"pdf-tipo-acessao-benfeitoria\": \"Tipo de Acessão/benfeitoria\", \"pdf-tipo-especializacao-edificacao\": \"Tipo de Especialização de edificação\", \"pdf-area-construida\": \"Área Construída\", \"pdf-area-projecao\": \"Área da Projeção\", \"pdf-numero-pavimentos\": \"Número de pavimentos\", \"pdf-vagas-garagem-cobertas\": \"Vagas de garagem cobertas\", \"pdf-vagas-garagem-descobertas\": \"Vagas de garagem descobertas\", \"pdf-divisao-interna\": \"Divisão interna\", \"pdf-materiais-construtivos\": \"Materiais construtivos internos\", \"pdf-estrutura\": \"Estrutura\", \"pdf-cobertura\": \"Cobertura\", \"pdf-fachada\": \"Fachada\", \"pdf-instalacoes-equipamentos\": \"Instalações e equipamentos\", \"pdf-tipo-edificacao\": \"Tipo de Edificação\", \"pdf-possui-elevador\": \"Possui Elevador\", \"pdf-tipo-projeto\": \"Tipo de projeto\", \"pdf-observacoes-gerais-edificacao\": \"Observações gerais da Edificação\", \"pdf-condicoes-acessibilidade\": \"Condições de acessibilidade\", \"pdf-detalhes-unidade-autonoma\": \"Detalhes da Unidade Autônoma\", \"pdf-numero-dormitorios\": \"Número de dormitórios\", \"pdf-memorial-descritivo\": \"Memorial Descritivo\", \"pdf-observacoes-gerais-unidade-autonoma\": \"Observações Gerais da unidade Autônoma\", \"pdf-pressupostos\": \"Pressupostos\", \"pdf-ressalvas-fatores\": \"ressalvas e fatores limitantes\", \"pdf-diagnostico-mercado\": \"Diagnóstico de mercado\", \"pdf-liquidez-imovel\": \"Liquidez do imóvel\", \"pdf-descricao-variaveis\": \"Descrição das variáveis\", \"pdf-atributos-avaliando\": \"Atributos do avaliando\", \"pdf-pesquisa-mercado\": \"Período de coleta de dados de mercado\", \"pdf-pressupostos-modelo\": \"Pressupostos do modelo de\", \"pdf-ferramenta-utilizada\": \"Ferramenta utilizada\", \"pdf-justificativa-valor-adotado\": \"Justificativa do valor adotado\", \"pdf-grau-fundamentacao-laudo-avaliacao\": \"Grau de fundamentação do Laudo de Avaliação\", \"pdf-justificativa-nao-atingimento\": \"Justificativa para não atingimento dos Graus mínimos dos itens\", \"pdf-amplitude-intervalo-confianca\": \"Amplitude do intervalo de confiança de 80% em torno da estimativa de tendência central\", \"pdf-grau-precisao-laudo\": \"Grau de Precisão do Laudo de Avaliação\", \"pdf-justificativa-nao-enquadramento-laudo\": \"Justificativa para o não enquadramento do laudo de avaliação quanto à precisão com base no diagnóstico de mercado\", \"pdf-valor-estimado-terreno\": \"Valor estimado do terreno\", \"pdf-id\": \"ID\", \"pdf-tipo\": \"Tipo\", \"pdf-vida-util\": \"Vida Útil\", \"pdf-idade-aparente\": \"Idade Aparente\", \"pdf-cub\": \"CUB\", \"pdf-valor\": \"Valor\", \"pdf-valor-estimado\": \"Valor Estimado\", \"pdf-motivo\": \"Motivo\", \"pdf-porcentagem\": \"Porcentagem\", \"pdf-encerramento\": \"Encerramento\", \"pdf-documento-assinado\": \"Documento assinado eletronicamente por\", \"pdf-em\": \"em\", \"pdf-relatorio-fotografico\": \"Relatório Fotográfico\", \"pdf-data-vistoria\": \"Data da vistoria\", \"pdf-anexos-avaliacao\": \"Anexos da aba avaliação\", \"pdf-matricula-cartorio\": \"ANEXO: Matrícula do cartório\", \"pdf-documentos-urbanisticos\": \"ANEXO: Documentos parâmetros urbanísticos\", \"pdf-memorial-descritivo-uniao\": \"Anexo: Memorial descritivo + Memorial descritivo da Área da União\", \"pdf-valor-estimado-cub-m2\": \"Valor estimado CUB(m²)\", \"pdf-title-imagens-imovel\": \"Imagens Imóvel\", \"pdf-title-imagens-benfeitorias\": \"Imagens Benfeitorias\", \"pdf-title-imagens-unidade-autonoma\": \"Imagens Unidade Autonoma\", \"pdf-descricao-outras-infraestruturas-urbanas\": \"Descrição de outras infraestruturas urbanas\", \"pdf-escola\": \"Escola\", \"pdf-seguranca\": \"Segurança\", \"pdf-comercio\": \"Comércio\", \"pdf-area-lazer\": \"Área Lazer\", \"pdf-posto-saude\": \"Posto de Saúde\", \"pdf-transporte-coletivo\": \"Transporte Coletivo\", \"pdf-rede-bancaria\": \"Rede Bancária\", \"pdf-vagas-deficientes-acessiveis\":\"Há vagas próximas dos acessos de circulação de pedestres, devidamente sinalizadas, para veículos que transportam pessoas portadoras de deficiência com dificuldade de locomoção permanente.\", \"pdf-acessos-interior-acessiveis\":\"Os acessos ao interior da edificação estão livres de barreiras arquitetônicas e de obstáculos que impeçam ou dificultem a acessibilidade de pessoa portadora de deficiência ou com mobilidade reduzida.\", \"pdf-banheiro-acessivel\":\"Há banheiro acessível, com seus equipamentos e acessórios distribuídos de maneira que possam ser utilizados por pessoa portadora de deficiência ou com mobilidade reduzida.\", \"pdf-elevador-acessivel\":\"Os elevadores oferecem condições de acessibilidade à pessoa portadora de deficiência ou com mobilidade reduzida.\", \"pdf-linearidade\": \"Linearidade\", \"pdf-normalidade\": \"Normalidade\", \"pdf-homocedasticidade\": \"Homocedasticidade\", \"pdf-verificacao-autocorrelacao\": \"Verificação da Autocorrelação\", \"pdf-colinearidade-multicolinearidade\": \"Colinearidade ou multicolinearidade\", \"pdf-pontos-influenciantes\": \"Pontos influenciantes ou outliers\", \"pdf-variaveis\": \"Variáveis\", \"pdf-variavel\": \"Variável\", \"pdf-utilizada\": \"Utilizada?\", \"pdf-amostras\": \"Amostras\", \"pdf-amostra\": \"Amostra\", \"pdf-informante-fonte\": \"Informante/Fonte\", \"pdf-codigo\": \"Código\", \"pdf-localizacao-imovel-amostras\": \"Localização do Imóvel e das Amostras\", \"pdf-modelo-adotado\": \"Modelo Adotado (função estimativa)\", \"pdf-estado-conservacao\": \"Estado de Conservação\", \"pdf-fator\": \"Fator\", \"pdf-justificativa-fonte\": \"Justificativa/Fonte\", \"pdf-justificativa\": \"Justificativa\", \"pdf-fotos-vistoria\": \"Fotos da Vistoria\", \"pdf-dado\": \"Dado\", \"pdf-quantificacao-custo-benfeitorias\":\"Quantificação de Custo das Benfeitorias\", \"pdf-quantificacao-custo\":\"Quantificação de Custo\", \"pdf-metodologia\":\"Metodologia\", \"pdf-enquadramento-grau-fundamentacao-metodo-evolutivo\":\"Enquadramento quanto ao grau de fundamentação do método evolutivo\", \"pdf-grau-3\": \"Grau III\", \"pdf-grau-2\": \"Grau II\", \"pdf-grau-1\": \"Grau I\", \"pdf-valor-imovel\": \"Valor Imóvel\", \"pdf-estimacao-custos\": \"Estimação de Custos\", \"pdf-custo-construcao\": \"Custo de Construção\", \"pdf-custo-reproducao\": \"Custo de Reprodução\", \"pdf-descricao-orcamento\": \"Descrição do Orçamento/Metodologia\", \"pdf-bdi\": \"BDI\", \"pdf-descricao-bdi-utilizado\": \"Descrição do BDI utilizado\", \"pdf-taxa-bdi\": \"Taxa BDI (%)\", \"pdf-depreciacao\": \"Depreciação\", \"pdf-descricao-depreciacao-calculada\": \"Descrição da Depreciação Calculada\", \"pdf-taxa-depreciacao\": \"Taxa BDI (%)\", \"pdf-valor-benfeitorias\": \"Valor das Benfeitorias\", \"pdf-resultado-avaliacao\": \"Resultado da Avaliação\", \"pdf-terreno-benfeitoria\": \"Terreno/Benfeitoria\", \"pdf-terreno\": \"Terreno\", \"pdf-fator-comercializacao\": \"Fator de Comercialização\", \"pdf-descricao-fator-comercializacao\": \"Descrição do Fator de Comercialização\", \"pdf-fc\": \"FC\", \"pdf-estimativa-valor-benfeitorias\": \"Estimativa de Valor das Benfeitorias\", \"pdf-fonte-valor-benfeitorias\": \"Fonte do Valor das Benfeitorias\", \"pdf-descricao-fonte\": \"Descrição da Fonte\", \"pdf-periodo-avaliacao\": \"Período da Avaliação\", \"pdf-valor-m2\": \"Valor do m²\", \"pdf-execucao-vistoria\": \"Execução da Vistoria\", \"pdf-observacoes-vistoria\": \"Anotações/Observações da Vistoria\", \"pdf-vistoria\": \"Vistoria\", \"pdf-n-inscricao\":\"N° de Inscrição\", \"pdf-n-processo\":\"N° de Processo\", \"pdf-forma-incorporacao\":\"Forma de Incorporação\", \"pdf-valor-encargo\":\"Valor de Encargo (R$)\", \"pdf-data-final-encargo\":\"Data Final do Encargo\", \"pdf-direito-adquirido\":\"Direito Adquirido/Incorporado\", \"pdf-doacao-com-encargo\":\"Doação com encargo?\", \"pdf-outro-contrato-pessoal\":\"Outro Contrato Pessoal\", \"pdf-situacao-incorporacao\":\"Situação da Incorporação\", \"pdf-fundamento\":\"Fundamento\", \"pdf-zona-area-interesse-social\":\"Zona/Área de interesse social (ZEIS)\", \"pdf-situado-em-area-seguranca\":\"Situado em área de segurança\", \"pdf-area-non-aedificandi\":\"Área Non Aedificandi\", \"pdf-tombado-como-patrimonio-historico\":\"Tomabado como patrimônio Histórico, Artístico e/ou Cultural\", \"pdf-outra-restricao-uso-ocupacao\":\"Outra restrição de uso/ocupação\", \"pdf-restricao-uso-ocupacao\":\"Restrição de uso/ocupação\", \"pdf-taxa-ocupacao\":\"Taxa Ocupação\", \"pdf-possui-etiqueta-conservacao-energia\":\"Possui Etiqueta Nacional de Conservação de Energia (ENCE)\", \"pdf-tipo-parcial\":\"Tipo de Parcial\", \"pdf-nivel\":\"Nível\", \"pdf-helio-caires\":\"Hélio de Caires\", \"pdf-jose-barbagelata\":\"José Barbagelata\", \"pdf-oscar-olave\":\"Oscar Olave\", \"pdf-modelo\":\"Modelo\", \"pdf-valor-unitario\":\"Valor Unitário\", \"pdf-area-imovel\":\"Área Imóvel (m²)\", \"pdf-lote-paradigma\":\"Lote Paradigma\", \"pdf-valor-unitario-adotado\":\"Valor Unitário Adotado (m²)\", \"pdf-despesas-com-urbanizacao\":\"Despesas com Urbanização\", \"pdf-despesas-unitaria-com-urbanizacao\":\"Despesas Unitárias com Urbanização\", \"pdf-valor-total-gleba\":\"Valor total da Gleba\", \"pdf-valor-m\":\"Valor do m²\", \"pdf-descricao-metodologia\":\"Descrição da Metodologia\", \"pdf-perdas-arruamento-lazer-areas-verdes\":\"Perdas em arruamento, lazer e áreas verdes (decimal)\", \"pdf-lucro-empreendedor\":\"Lucro do Empreendedor\", \"pdf-fonte\":\"Fonte\", \"pdf-valor-terreno\":\"Valor Terreno\", \"pdf-relatorio-fotografico-anexo\":\"Relatório fotográfico em anexo.\" }");
$templateCache.put("scripts-avaliacao/pages/laudo-relatorio-avaliacao/views/cadastrarLaudoRelatorioAvaliacao.html","<div class=separarPaddingRows layout=row flex layout-align=\"start start\"><label class=titulo-pagina>{{\'title-laudo-relatorio-avaliacao\' | translate}}</label></div><div ng-cloak><md-content><md-tabs md-dynamic-height md-border-bottom><md-tab id=CANCELAMENTO md-selected=\"tabSelect==CANCELAMENTO\" md-active=\"tabSelect==CANCELAMENTO\" ng-disabled=\"tabSelect!=CANCELAMENTO\" label=\"{{\'tab-cancelamento\' | translate}}\" ng-if=false><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==CANCELAMENTO\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><cancelamento-laudo flex=70></cancelamento-laudo></div></md-content></md-tab><md-tab id=IDENTIFICACAO md-selected=\"tabSelect==IDENTIFICACAO\" md-active=\"tabSelect==IDENTIFICACAO\" ng-click=selecionarAba(IDENTIFICACAO); label=\"{{\'tab-identicacao\' | translate}}\"><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==IDENTIFICACAO\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><identificacao-laudo identificacao=laudoRelatorioAvaliacao.identificacao rip-imovel=ripImovel limpar-dados-lista-benfeitorias=limparDadosListaBenfeitorias is-rip-bloqueado=isRipBloqueado formulario-validacao=formularioIdentificacao lista-responsaveis-tecnicos=listaResponsaveisTecnicos lista-normas-utilizadas=listaNormasUtilizadas benfeitorias=laudoRelatorioAvaliacao.benfeitorias imovel=imovel buscar-dados-imovel-por-rip=buscarDadosImovelPorRip detalhar-laudo-relatorio=detalharLaudoRelatorio flex=70></identificacao-laudo></div></md-content></md-tab><md-tab id=DETALHES_IMOVEL md-selected=\"tabSelect==DETALHES_IMOVEL\" md-active=\"tabSelect==DETALHES_IMOVEL\" ng-disabled=desabilitarAba ng-click=selecionarAba(DETALHES_IMOVEL); label=\"{{\'tab-detalhes-imovel\' | translate}}\"><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==DETALHES_IMOVEL\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><detalhes-imovel-laudo rip=ripImovel detalhes-imovel=laudoRelatorioAvaliacao.detalhesImovel anexo-laudo-relatorio=laudoRelatorioAvaliacao.anexo lista-matriculas-transcricao=listaMatriculasTranscricao lista-certidoes-cartoriais=listaCertidoesCartoriais imovel=imovel formulario-validacao=formularioDetalhesImovel flex=70 dados-enderecamento=dadosEnderecamento dados-proprietario=dadosProprietario detalhar-laudo-relatorio=detalharLaudoRelatorio></detalhes-imovel-laudo></div></md-content></md-tab><md-tab id=TERRENO_IMOVEL md-selected=\"tabSelect==TERRENO_IMOVEL\" md-active=\"tabSelect==TERRENO_IMOVEL\" ng-disabled=desabilitarAba ng-click=selecionarAba(TERRENO_IMOVEL); label=\"{{\'tab-terreno-imovel\' | translate}}\" ng-if=ripImovel><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==TERRENO_IMOVEL\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><terreno-imovel-laudo flex=70 terreno-imovel=$parent.laudoRelatorioAvaliacao.terrenoImovel documentos-parametros-urbanisticos=$parent.documentosParametrosUrbanisticos anexo-laudo-relatorio=laudoRelatorioAvaliacao.anexo imovel=$parent.imovel terreno=$parent.terreno formulario-validacao=$parent.formularioTerrenoImovel detalhar-laudo-relatorio=detalharLaudoRelatorio></terreno-imovel-laudo></div></md-content></md-tab><md-tab id=BENFEITORIAS md-selected=\"tabSelect==BENFEITORIAS\" md-active=\"tabSelect==BENFEITORIAS\" ng-disabled=desabilitarAba ng-click=selecionarAba(BENFEITORIAS); label=\"{{\'tab-benfeitorias\' | translate}}\" ng-if=\"ripImovel && (listaBenfeitoriaAvaliacao && listaBenfeitoriaAvaliacao.length > 0) && !unidadeAutonomaIncorporacao\"><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==BENFEITORIAS\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><benfeitorias-laudo flex=70 benfeitorias=$parent.laudoRelatorioAvaliacao.benfeitorias lista-benfeitoria-avaliacao=$parent.listaBenfeitoriaAvaliacao redirecionar-cadastrar-documento-edificacao-benfeitoria=redirecionarCadastrarDocumentoEdificacaoBenfeitoria formulario-validacao=$parent.formularioBenfeitorias detalhar-laudo-relatorio=detalharLaudoRelatorio></benfeitorias-laudo></div></md-content></md-tab><md-tab id=UNIDADE_AUTONOMA md-selected=\"tabSelect==UNIDADE_AUTONOMA\" md-active=\"tabSelect==UNIDADE_AUTONOMA\" ng-disabled=desabilitarAba ng-click=selecionarAba(UNIDADE_AUTONOMA); label=\"{{\'tab-unidade-autonoma\' | translate}}\" ng-if=\"ripImovel && unidadeAutonomaIncorporacao && !(listaBenfeitoriaAvaliacao && listaBenfeitoriaAvaliacao.length > 0) \"><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==UNIDADE_AUTONOMA\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><unidade-autonoma-laudo flex=70 unidade-autonoma=laudoRelatorioAvaliacao.unidadeAutonoma redirecionar-cadastrar-documento-edificacao-unidade-autonoma=redirecionarCadastrarDocumentoEdificacaoUnidadeAutonoma imovel=imovel formulario-validacao=$parent.formularioUnidadeAutonoma unidade-autonoma-incorporacao=unidadeAutonomaIncorporacao detalhar-laudo-relatorio=detalharLaudoRelatorio></unidade-autonoma-laudo></div></md-content></md-tab><md-tab id=VISTORIA md-selected=\"tabSelect==VISTORIA\" md-active=\"tabSelect==VISTORIA\" ng-disabled=desabilitarAba ng-click=selecionarAba(VISTORIA); label=\"{{\'tab-vistoria\' | translate}}\"><md-content flex=100><div layout=row layout-xs=column flex=100 ng-if=carregaVistoria><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==VISTORIA\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><vistoria-laudo flex=70 vistoria=laudoRelatorioAvaliacao.vistoria lista-fotos-imovel=listaFotosImovel tipo-avaliacao=laudoRelatorioAvaliacao.identificacao.tipo formulario-validacao=$parent.formularioVistoria lista-benfeitoria=listaBenfeitoriaAvaliacao unidade-autonoma=unidadeAutonomaIncorporacao detalhar-laudo-relatorio=detalharLaudoRelatorio vistoria-lista-fotos-imovel=vistoriaListaFotosImovel vistoria-lista-fotos-benfeitorias=vistoriaListaFotosBenfeitorias vistoria-lista-fotos-unidade-autonoma=vistoriaListaFotosUnidadeAutonoma lista-fotos-benfeitorias=listaFotosBenfeitorias identificacao=laudoRelatorioAvaliacao.identificacao anexo-laudo-relatorio=laudoRelatorioAvaliacao.anexo></vistoria-laudo></div></md-content></md-tab><md-tab id=AVALIACAO md-selected=\"tabSelect==AVALIACAO\" md-active=\"tabSelect==AVALIACAO\" ng-disabled=desabilitarAba ng-click=selecionarAba(AVALIACAO); label=\"{{(laudoRelatorioAvaliacao.identificacao.tipo == \'LAUDO\' && laudoRelatorioAvaliacao.identificacao.metodoAvaliacao == \'EVOLUTIVO\') ? \'tab-avaliacao-terreno\'  : \'tab-avaliacao\'| translate}}\"><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==AVALIACAO\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><avaliacao-laudo ng-if=\"laudoRelatorioAvaliacao.identificacao.metodologia != \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\' && laudoRelatorioAvaliacao.identificacao.metodologia != \'GLEBA_URBANIZAVEL\'\" flex=70 avaliacao=laudoRelatorioAvaliacao.avaliacao laudo-relatorio-avaliacao=laudoRelatorioAvaliacao redirecionar-selecionar-amostras=redirecionarSelecionarAmostras formulario-validacao=$parent.formularioAvaliacao redirecionar-adicionar-variavel-amostras=redirecionarAdicionarVariavelAmostras lista-benfeitoria-avaliacao=listaBenfeitoriaAvaliacao unidade-autonoma-incorporacao=unidadeAutonomaIncorporacao redirecionar-informar-valor-edificacao=redirecionarInformarValorEdificacao redirecionar-informar-valor-cub=redirecionarInformarValorCub tipo-valor-laudo=tipoValorLaudo ferramenta-analitica=laudoRelatorioAvaliacao.identificacao.ferramentaAnalitica detalhar-laudo-relatorio=detalharLaudoRelatorio salvar-como-rascunho-laudo-relatorio=confirmarSalvarComoRascunhoLaudoRelatorioParaAdicionarVariavel unidade-medida=\"terreno.unidadeMedida == HECTARE ? \'ha\' : \'m²\'\" identificacao=laudoRelatorioAvaliacao.identificacao></avaliacao-laudo><avaliacao-estimativa ng-if=\"laudoRelatorioAvaliacao.identificacao.metodologia == \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\'\" flex=70 avaliacao=laudoRelatorioAvaliacao.avaliacao laudo-relatorio-avaliacao=laudoRelatorioAvaliacao formulario-validacao=$parent.formularioAvaliacao lista-benfeitoria-avaliacao=listaBenfeitoriaAvaliacao unidade-autonoma-incorporacao=unidadeAutonomaIncorporacao redirecionar-informar-valor-edificacao=redirecionarInformarValorEdificacao tipo-valor-laudo=tipoValorLaudo detalhar-laudo-relatorio=detalharLaudoRelatorio identificacao=laudoRelatorioAvaliacao.identificacao quantificacao-custo=laudoRelatorioAvaliacao.quantificacaoCusto uf=imovel.enderecamento.uf redirecionar-calcular-area-equivalente=redirecionarCalcularAreaEquivalente area-total-terreno=laudoRelatorioAvaliacao.terrenoImovel.terreno.areaTotal anexo-laudo-relatorio=laudoRelatorioAvaliacao.anexo></avaliacao-estimativa><avaliacao-gleba-urbanizavel ng-if=\"laudoRelatorioAvaliacao.identificacao.metodologia == \'GLEBA_URBANIZAVEL\'\" flex=70 avaliacao=laudoRelatorioAvaliacao.avaliacao laudo-relatorio-avaliacao=laudoRelatorioAvaliacao redirecionar-selecionar-amostras=redirecionarSelecionarAmostras formulario-validacao=$parent.formularioAvaliacao redirecionar-adicionar-variavel-amostras=redirecionarAdicionarVariavelAmostras lista-benfeitoria-avaliacao=listaBenfeitoriaAvaliacao unidade-autonoma-incorporacao=unidadeAutonomaIncorporacao redirecionar-informar-valor-edificacao=redirecionarInformarValorEdificacao tipo-valor-laudo=tipoValorLaudo detalhar-laudo-relatorio=detalharLaudoRelatorio salvar-como-rascunho-laudo-relatorio=confirmarSalvarComoRascunhoLaudoRelatorioParaAdicionarVariavel salvar-como-rascunho-laudo-relatorio-cadastrar-avaliacao=salvarComoRascunhoLaudoRelatorio identificacao=laudoRelatorioAvaliacao.identificacao area-total-terreno=laudoRelatorioAvaliacao.terrenoImovel.terreno.areaTotal anexo-laudo-relatorio=laudoRelatorioAvaliacao.anexo></avaliacao-gleba-urbanizavel></div></md-content></md-tab><md-tab ng-if=\"laudoRelatorioAvaliacao.identificacao.metodologia != \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\' && laudoRelatorioAvaliacao.identificacao.metodologia != \'GLEBA_URBANIZAVEL\'\" ng-disabled=desabilitarAba ng-disabled=desabilitarAba ng-click=selecionarAba(RESULTADO); label=\"{{(laudoRelatorioAvaliacao.identificacao.tipo == \'LAUDO\' && laudoRelatorioAvaliacao.identificacao.metodoAvaliacao == \'EVOLUTIVO\') ? \'tab-resultados-avaliacao-terreno\'  : \'tab-resultados-avaliacao\'| translate}}\" md-selected=\"tabSelect==RESULTADO\" md-active=\"tabSelect==RESULTADO\"><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==RESULTADO\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><avaliacao-laudo-resultados ng-if=\"laudoRelatorioAvaliacao.identificacao.tipo == \'LAUDO\'\" flex=70 avaliacao=laudoRelatorioAvaliacao.avaliacao laudo-relatorio-avaliacao=laudoRelatorioAvaliacao formulario-validacao=$parent.formularioResultadosLaudo ferramenta-analitica=laudoRelatorioAvaliacao.identificacao.ferramentaAnalitica area-total-terreno=laudoRelatorioAvaliacao.terrenoImovel.terreno.areaTotal lista-benfeitoria-avaliacao=listaBenfeitoriaAvaliacao unidade-autonoma-incorporacao=unidadeAutonomaIncorporacao redirecionar-informar-valor-edificacao=redirecionarInformarValorEdificacao redirecionar-informar-valor-cub=redirecionarInformarValorCub tipo-valor-laudo=tipoValorLaudo detalhar-laudo-relatorio=detalharLaudoRelatorio unidade-medida=\"terreno.unidadeMedida == HECTARE ? \'ha\' : \'m²\'\" identificacao=laudoRelatorioAvaliacao.identificacao anexo-laudo-relatorio=laudoRelatorioAvaliacao.anexo></avaliacao-laudo-resultados><avaliacao-laudo-resultados-relatorio ng-if=\"laudoRelatorioAvaliacao.identificacao.tipo == \'RELATORIO\'\" flex=70 avaliacao=laudoRelatorioAvaliacao.avaliacao laudo-relatorio-avaliacao=laudoRelatorioAvaliacao formulario-validacao=$parent.formularioResultadosRelatorio ferramenta-analitica=laudoRelatorioAvaliacao.identificacao.ferramentaAnalitica area-total-terreno=laudoRelatorioAvaliacao.terrenoImovel.terreno.areaTotal lista-benfeitoria-avaliacao=listaBenfeitoriaAvaliacao unidade-autonoma-incorporacao=unidadeAutonomaIncorporacao redirecionar-informar-valor-edificacao=redirecionarInformarValorEdificacao redirecionar-informar-valor-cub=redirecionarInformarValorCub tipo-valor-laudo=tipoValorLaudo detalhar-laudo-relatorio=detalharLaudoRelatorio unidade-medida=\"terreno.unidadeMedida == HECTARE ? \'ha\' : \'m²\'\" identificacao=laudoRelatorioAvaliacao.identificacao anexo-laudo-relatorio=laudoRelatorioAvaliacao.anexo></avaliacao-laudo-resultados-relatorio></div></md-content></md-tab><md-tab ng-if=\"laudoRelatorioAvaliacao.identificacao.metodoAvaliacao && laudoRelatorioAvaliacao.identificacao.metodoAvaliacao == \'EVOLUTIVO\'\" ng-disabled=desabilitarAba ng-click=selecionarAba(QUANTIFICACAO); label=\"{{\'tab-quantificacao-custo\'| translate}}\" md-selected=\"tabSelect==QUANTIFICACAO\" md-active=\"tabSelect==QUANTIFICACAO\"><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==QUANTIFICACAO\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><quantificacao-custo-laudo ng-show=\"laudoRelatorioAvaliacao.identificacao.quantificacaoCustoBenfeitoria == \'ORCAMENTO\'\" flex=70 quantificacao-custo=laudoRelatorioAvaliacao.quantificacaoCusto formulario-validacao=$parent.formularioQuantificacaoOrcamento detalhar-laudo-relatorio=detalharLaudoRelatorio lista-benfeitoria-avaliacao=listaBenfeitoriaAvaliacao identificacao=laudoRelatorioAvaliacao.identificacao anexo-laudo-relatorio=laudoRelatorioAvaliacao.anexo></quantificacao-custo-laudo><quantificacao-custo-cub-laudo ng-show=\"laudoRelatorioAvaliacao.identificacao.quantificacaoCustoBenfeitoria == \'CUB_PROJETO_SEMELHANTE\'\" flex=70 quantificacao-custo=laudoRelatorioAvaliacao.quantificacaoCusto formulario-validacao=$parent.formularioQuantificacao detalhar-laudo-relatorio=detalharLaudoRelatorio lista-benfeitoria-avaliacao=listaBenfeitoriaAvaliacao identificacao=laudoRelatorioAvaliacao.identificacao uf=imovel.enderecamento.uf redirecionar-calcular-area-equivalente=redirecionarCalcularAreaEquivalente anexo-laudo-relatorio=laudoRelatorioAvaliacao.anexo></quantificacao-custo-cub-laudo></div></md-content></md-tab><md-tab ng-if=\"laudoRelatorioAvaliacao.identificacao.metodologia == \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\' || laudoRelatorioAvaliacao.identificacao.metodoAvaliacao == \'EVOLUTIVO\'\" ng-disabled=desabilitarAba ng-click=selecionarAba(RESULTADO_AVALIACAO); label=\"{{\'tab-resultado-avaliacao\'| translate}}\" md-selected=\"tabSelect==RESULTADO_AVALIACAO\" md-active=\"tabSelect==RESULTADO_AVALIACAO\"><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==RESULTADO_AVALIACAO\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><resultado-avaliacao-laudo-evolutivo flex=70 quantificacao-custo=laudoRelatorioAvaliacao.quantificacaoCusto resultado-avaliacao=laudoRelatorioAvaliacao.resultadoAvaliacao lista-benfeitoria-avaliacao=listaBenfeitoriaAvaliacao avaliacao=laudoRelatorioAvaliacao.avaliacao enquadramento=laudoRelatorioAvaliacao.avaliacao.enquadramento identificacao=laudoRelatorioAvaliacao.identificacao formulario-validacao=$parent.formularioResultadoAvaliacao detalhar-laudo-relatorio=detalharLaudoRelatorio anexo-laudo-relatorio=laudoRelatorioAvaliacao.anexo></resultado-avaliacao-laudo-evolutivo></div></md-content></md-tab><md-tab id=ANEXOS md-selected=\"tabSelect==ANEXOS\" md-active=\"tabSelect==ANEXOS\" ng-disabled=desabilitarAba ng-click=selecionarAba(ANEXOS); label=Anexos><md-content flex=100><div layout=row layout-xs=column flex=100><area-lateral-mapa-slick flex=30 ng-if=\"tabSelect==ANEXOS\" lista-imagens-imovel=$parent.listaFotosImovel rip=$parent.ripImovel ponto-imovel=$parent.pontoImovel capturar-mapa=$parent.capturarMapa></area-lateral-mapa-slick><avaliacao-laudo-anexos flex=70 identificacao=laudoRelatorioAvaliacao.identificacao laudo=laudoRelatorioAvaliacao terreno=laudoRelatorioAvaliacao.terrenoImovel anexo=laudoRelatorioAvaliacao.anexo formulario-validacao=$parent.formularioAnexo detalhar-laudo-relatorio=detalharLaudoRelatorio></avaliacao-laudo-anexos></div></md-content></md-tab></md-tabs></md-content><div ng-show=!detalharLaudoRelatorio layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=retornarParaAbaAnterior(); ng-if=\"tabSelect && tabSelect != IDENTIFICACAO\"><label>{{\'button-retornar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=redirecionarConsultarImovelLaudoRelatorio();><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarComoRascunhoLaudoRelatorio(null);><label>{{\'button-salvar-rascunho\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=avancarParaProximaAba(); ng-if=\"tabSelect && tabSelect != ANEXOS\"><label>{{\'button-avancar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=concluirCadastroLaudoRelatorioAvaliacao(); ng-if=\"tabSelect && tabSelect == ANEXOS\"><label>{{\'button-concluir\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=\"concluirCadastroLaudoRelatorioAvaliacao(\'assinar\');\" ng-if=\"tabSelect && tabSelect == ANEXOS && verificarResponsaveisTecnicos()\"><label>{{\'button-concluir-assinar\' | translate}}</label></md-button></div></div></div><div ng-show=detalharLaudoRelatorio layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=navegarEntreAbas(RETORNAR); ng-if=\"tabSelect && tabSelect != IDENTIFICACAO\"><label>{{\'button-retornar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=redirecionarConsultarImovelLaudoRelatorio();><label>{{\'button-fechar\' | translate}}</label></md-button><md-button ng-show=\"!verificarAssinaturaResponsavelTecnico() && usuarioLogado.AVALIACAO_INCLUIR_EDITAR_LAUDORELATORIO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(laudoRelatorioAvaliacao.detalhesImovel.imovel.enderecamento.uf) != -1)\" class=\"md-primary md-raised m-l-15\" type=button ng-click=redirecionarEditarLaudoRelatorio()><label>{{\'button-editar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=redirecionarAssinarLaudoRelatorio() ng-if=\"laudoRelatorioAvaliacao.situacaoLaudoRelatorio == \'AGUARDANDO_ASSINATURA\'\"><label>{{\'button-assinar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=navegarEntreAbas(AVANCAR); ng-if=\"tabSelect && tabSelect != ANEXOS\"><label>{{\'button-avancar\' | translate}}</label></md-button></div></div></div></div>");
$templateCache.put("scripts-avaliacao/pages/laudo-relatorio-avaliacao/views/consultarLaudoRelatorioAvaliacao.html","<form name=formularioConsultarLaudoRelatorio><md-content><toaster-container></toaster-container><div class=separarPaddingRows layout=row flex layout-align=\"start start\"><label class=titulo-pagina>{{\'title-consultar_laudo_relatorio\' | translate}}</label></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tipo\' | translate}}</label><md-select ng-model=filtroConsulta.tipo name=\"{{\'label-tipo\' | translate}}\"><md-option ng-repeat=\"(k,v) in tiposIdentificacao\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100 ng-show=\"filtroConsulta.tipo==\'LAUDO\'\"><label>{{\'label-metodo_avaliacao\' | translate}}</label><md-select ng-model=filtroConsulta.metodoAvaliacao name=\"{{\'label-metodo_avaliacao\' | translate}}\"><md-option ng-repeat=\"(k,v) in metodosAvaliacao\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div flex=100 layout=row style=\"margin-top: -20px\"><div flex=50></div><div flex=50 layout-align=\"end center\"><label class=marginLeft2>{{\'label-periodo-assinatura-imovel\' | translate}}</label></div></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-situacao\' | translate}}</label><md-select ng-model=filtroConsulta.situacaoLaudo name=\"{{\'label-situacao\' | translate}}\" multiple><md-option ng-repeat=\"(k,v) in situacoesLaudoRelatorio\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-container flex=50 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row layout-xs=row><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=filtroConsulta.dataInicialAssinatura md-max-date=filtroConsulta.dataFinalAssinatura></md-datepicker></div><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-model=filtroConsulta.dataFinalAssinatura md-max-date=filtroConsulta.dataInicialAssinatura></md-datepicker></div></div></md-container></div><div layout=row layout-xs=column flex-sm=column flex-gt-sm=column><md-input-container class=md-block flex=50 flex-gt-sm=100 flex-xs=100 flex-sm=100><label>{{\'label-numero_laudo_relatorio\' | translate}}</label><input ng-model=filtroConsulta.numeroLaudo name=\"{{\'label-numero_laudo_relatorio\' | translate}}\"></md-input-container><md-input-container class=md-block flex=50 flex-gt-sm=100 flex-xs=100 flex-sm=100><label>{{\'label-validade\' | translate}}</label><md-select ng-model=filtroConsulta.validade name=\"{{\'label-validade\' | translate}}\"><md-option ng-repeat=\"(k,v) in validadeLaudo\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'tr-rip\' | translate}}</label><input ng-model=filtroConsulta.rip name=rip maxlength=8></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-tipo-imovel\' | translate}}</label><md-select ng-model=filtroConsulta.idTipoImovel name=\"{{\'label-tipo-imovel\' | translate}}\"><span ng-repeat=\"tipoImovel in listaTipoImovel\"><md-option ng-value=tipoImovel.id ng-if=\"tipoImovel.descricao != UNIDADE_AUTONOMA\">{{tipoImovel.descricao}}</md-option><md-optgroup label={{tipoImovel.descricao}} ng-value=tipoImovel.id ng-if=\"tipoImovel.descricao == UNIDADE_AUTONOMA\"><md-option ng-repeat=\"tipoUnidadeAut in tiposUnidadeAutonoma\" ng-value=tipoUnidadeAut.descricao>{{tipoUnidadeAut.descricao}}</md-option></md-optgroup></span></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-natureza-imovel\' | translate}}</label><md-select ng-model=filtroConsulta.idNaturezaImovel name=\"{{\'label-natureza-imovel\' | translate}}\"><md-option ng-repeat=\"naturezaImovel in listaNaturezaImovel\" ng-value=naturezaImovel.id>{{naturezaImovel.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=30 flex-xs=100 flex-sm=100><label>{{\'label-pais\' | translate}}</label><md-select ng-model=filtroConsulta.pais name=\"{{\'label-pais\' | translate}}\"><md-option ng-repeat=\"pais in listaPaises\" ng-value=pais.descricao>{{pais.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=30 flex-xs=100 flex-sm=100 flex-offset=5 ng-show=\"!filtroConsulta.pais || filtroConsulta.pais == BRASIL\"><label>{{\'label-uf\' | translate}}</label><md-select ng-model=filtroConsulta.uf name=\"{{\'label-uf\' | translate}}\" ng-change=pesquisarMunicipiosPorUf() placeholder=\"{{\'label-uf\' | translate}}\"><md-option ng-repeat=\"uf in listaUFs\" ng-value=uf.sigla>{{uf.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=30 flex-xs=100 flex-sm=100 flex-offset=5 ng-show=\"!filtroConsulta.pais || filtroConsulta.pais == BRASIL\"><label>{{\'label-cep\' | translate}}</label><input ng-model=filtroConsulta.cep name=\"{{\'label-cep\' | translate}}\" ng-change=consultarLocalidadeEnderecoCep(); ui-br-cep-mask></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100 ng-show=\"!filtroConsulta.pais || filtroConsulta.pais == BRASIL\"><label>{{\'label-municipio\' | translate}}</label><md-select ng-model=filtroConsulta.municipio name=\"{{\'label-municipio\' | translate}}\"><md-option ng-repeat=\"municipio in listaMunicipios | orderBy: \'nome\'\" ng-value=municipio.nome>{{municipio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100 ng-show=\"filtroConsulta.pais && filtroConsulta.pais != BRASIL\"><label>{{\'label-cidade-exterior\' | translate}}</label><md-select ng-model=filtroConsulta.cidadeExterior name=\"{{\'label-cidade-exterior\' | translate}}\"><md-option></md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100 ng-show=\"!filtroConsulta.pais || filtroConsulta.pais == BRASIL\"><label>{{\'label-bairro-logradouro\' | translate}}</label><input ng-model=filtroConsulta.bairro name=\"{{\'label-bairro-logradouro\' | translate}}\"></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-proprietario_titular_imovel\' | translate}}</label><md-select ng-model=filtroConsulta.tipoProprietario name=\"{{\'label-proprietario_titular_imovel\' | translate}}\"><md-option ng-value=UNIAO>{{UNIAO}}</md-option><md-option ng-value=AUTARQUIA_FUNDACAO>{{AUTARQUIA_FUNDACAO}}</md-option><md-option ng-value=TERCEIROS>{{TERCEIROS}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-responsavel-tecnico\' | translate}}</label><input ng-model=filtroConsulta.campoResponsavelTecnico name=\"{{\'label-responsavel-tecnico\' | translate}}\"></md-input-container></div><div layout=row layout-xs=column flex=100 flex-sm=column flex-sm=100 flex-xs=100><div layout=row class=barraBotoesSalvarCancelar flex><md-dialog-actions flex layout=row layout-align=\"end center\" layout-xs=column flex-xs layout-align-xs=\"start start\" layout-md=column layout-align-md=\"start start\" layout-sm=column layout-align-sm=\"start start\" flex-sm=100><md-button class=\"btn-size-medium btn-avaliacao limpar\" ng-click=limparCamposPesquisa();><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=\"btn-size-medium btn-avaliacao pesquisar\" ng-click=buscarLaudosRelatoriosPaginacao();><label>{{\'button-pesquisar\' | translate}}</label></md-button><md-button ng-if=\"usuarioLogado.AVALIACAO_INCLUIR_EDITAR_LAUDORELATORIO == \'true\'\" class=\"btn-size-medium btn-avaliacao novo\" ng-click=redirecionarCadastrarLaudoRelatorioAvaliacao();><label>{{\'button-novo\' | translate}}</label></md-button></md-dialog-actions></div></div><div class=caixa-tabela-resultado layout=row layout-align=\"center start\" ng-show=\"listaDadosLaudoRelatorio.length > 0\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'tr-numero-laudo-relatorio\' | translate}}</th><th md-column>{{\'tr-tipo\' | translate}}</th><th md-column>{{\'tr-metodo-avaliacao\' | translate}}</th><th md-column>{{\'tr-situacao\' | translate}}</th><th md-column>{{\'tr-data-da-assinatura\' | translate}}</th><th md-column>{{\'tr-valor-rs\' | translate}}</th><th md-column>{{\'tr-imovel\' | translate}}</th><th md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"laudoRelatorio in listaDadosLaudoRelatorio\"><td md-cell><p class=text-center>{{laudoRelatorio.numero}}</p></td><td md-cell><label ng-bind=tiposIdentificacao[laudoRelatorio.identificacao.tipo]></label></td><td md-cell><label ng-bind=metodosAvaliacao[laudoRelatorio.identificacao.metodoAvaliacao]></label></td><td md-cell><label ng-bind=situacoesLaudoRelatorio[laudoRelatorio.situacaoLaudoRelatorio]></label></td><td md-cell><label ng-if=laudoRelatorio.dataAssinatura ng-bind=\"laudoRelatorio.dataAssinatura | date: \'dd/MM/yyyy\' : \'-0300\'\"></label><p ng-if=!laudoRelatorio.dataAssinatura class=text-center>-</p></td><td md-cell><p ng-class=\"validarValorLaudoRelatorio(laudoRelatorio) == \'-\' ? \'text-center\' : \'\'\">{{validarValorLaudoRelatorio(laudoRelatorio);}}</p></td><td md-cell><span ng-bind=\"(\'tr-rip\' | translate) + \':\' + laudoRelatorio.identificacao.rip + \' \' + \' \\n\'\n                                                        + (\'label-endereco\' | translate) + \':\' + laudoRelatorio.detalhesImovel.imovel.enderecamento.logradouro + \', \'\n                                                        + (\'label-numero\' | translate) + \' \' + laudoRelatorio.detalhesImovel.imovel.enderecamento.numero + \', \'\n                                                        + laudoRelatorio.detalhesImovel.imovel.enderecamento.bairro + \', \'\n                                                        + laudoRelatorio.detalhesImovel.imovel.enderecamento.municipio + \'/\' + laudoRelatorio.detalhesImovel.imovel.enderecamento.uf + \' - \'\n                                                        + laudoRelatorio.detalhesImovel.imovel.enderecamento.pais + \', \'\n                                                        + (\'tr-cep\' | translate) + \': \' + (laudoRelatorio.detalhesImovel.imovel.enderecamento.cep | cep)\"></span></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button ng-if=\"usuarioLogado.AVALIACAO_EXCLUIR_LAUDORELATORIO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(laudoRelatorio.detalhesImovel.imovel.enderecamento.uf) != -1)\" ng-click=redirecionarEditarLaudoRelatorio(laudoRelatorio.id) ng-click=removerLaudoRelatorio(laudoRelatorio.id) ng-show=\"laudoRelatorio.situacaoLaudoRelatorio==EM_PREENCHIMENTO || laudoRelatorio.situacaoLaudoRelatorio==AGUARDANDO_ASSINATURA\" class=\"md-fab md-raised md-mini\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>{{\'button-excluir\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=redirecionarAssinarLaudoRelatorio(laudoRelatorio.id) ng-if=\"laudoRelatorio.situacaoLaudoRelatorio==AGUARDANDO_ASSINATURA && usuarioLogadoHomologador(laudoRelatorio.identificacao.responsaveisTecnicos)\" class=\"md-fab md-raised md-mini\"><md-icon ng-bind=\"\'done\'\"><md-tooltip>{{\'button-assinar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"!verificarAssinaturaResponsavelTecnico(laudoRelatorio.identificacao.responsaveisTecnicos) && usuarioLogado.AVALIACAO_INCLUIR_EDITAR_LAUDORELATORIO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(laudoRelatorio.detalhesImovel.imovel.enderecamento.uf) != -1)\" ng-click=redirecionarEditarLaudoRelatorio(laudoRelatorio.id) ng-show=\"laudoRelatorio.situacaoLaudoRelatorio==EM_PREENCHIMENTO || laudoRelatorio.situacaoLaudoRelatorio==AGUARDANDO_ASSINATURA\" class=\"md-fab md-raised md-mini\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'button-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=redirecionarDetalharLaudoRelatorio(laudoRelatorio.id); class=\"md-fab md-raised md-mini\" aria-label=\"Detalhar Imóvel\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>{{\'button-detalhar\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"abrirDialogMapaDetalharImovel($event, laudoRelatorio.identificacao.rip)\"><md-icon ng-bind=\"\'map\'\"><md-tooltip>{{\'label-ver-localizacao\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=buscarLaudosRelatoriosPaginacao md-page-select></md-table-pagination></div></div></md-content></form>");
$templateCache.put("scripts-avaliacao/pages/laudo-relatorio-avaliacao/views/visualizarLaudoRelatorioPdf.html","<div ng-cloak><md-content><div class=pdfEspacamento><div class=logo-assinar-termo><img width=150px src=assets/img/img-avaliacao/logo_pdf_laudo_avaliacao.png></div><div layout=column class=positionRelative flex=100 style=\"min-height: 160px\"><div><h3 class=alinhamentoTela>{{laudoRelatorio.identificacao.orgao}}</h3><h3 class=alinhamentoTela>{{laudoRelatorio.identificacao.unidade}}</h3><h3 class=alinhamentoTela>{{laudoRelatorio.identificacao.subunidade}}</h3><h1 class=\"negritoTexto alinhamentoTela\">{{\'pdf-laudo-de-avaliacao\' | translate}}{{\' \' + laudoRelatorio.numero}}</h1></div></div><div align=center class=pdfMarginTop><div align=justify><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-rip\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.identificacao.rip></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-finalidade\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.identificacao.finalidade></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-solicitante\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.identificacao.solicitante></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-objetivo\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.identificacao.objetivo></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-normas-utilizadas\' | translate}}</label>&nbsp;<label ng-bind=laudoRelatorio.identificacao.normasUtilizadas></label></div><div class=pdfEspacamentoLabels><table border=1 style=\"width: 100%\"><tr ng-repeat=\"normasUtilizadas in laudoRelatorio.identificacao.listaNormasUtilizadas\"><td>{{$index + 1}}</td><td style=\"text-align: left\">{{normasUtilizadas.norma.nome}}</td></tr></table></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.identificacao.metodoAvaliacao><label class=negritoTexto>{{\'pdf-metodo-de-avaliacao\' | translate}}:</label>&nbsp;<label ng-bind=metodosAvaliacao[laudoRelatorio.identificacao.metodoAvaliacao]></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.identificacao.metodologia><label class=negritoTexto>{{\'pdf-metodologia\' | translate}}:</label>&nbsp;<label ng-bind=\"laudoRelatorio.identificacao.metodologia == \'OUTRO\' ? laudoRelatorio.identificacao.metodologiaOutros : listaMetodologia[laudoRelatorio.identificacao.metodologia]\"></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria><label class=negritoTexto>{{\'pdf-quantificacao-custo-benfeitorias\' | translate}}:</label>&nbsp;<label ng-bind=listaQuantificacaoCustoBenfeitorias[laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria]></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.identificacao.tratamento><label class=negritoTexto>{{\'pdf-tratamento\' | translate}}:</label>&nbsp;<label ng-bind=tratamentosIdentificacao[laudoRelatorio.identificacao.tratamento]></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.identificacao.ferramentaAnalitica><label class=negritoTexto>{{\'pdf-ferramenta-analitica\' | translate}}:</label>&nbsp;<label ng-bind=ferramentasAnaliticas[laudoRelatorio.identificacao.ferramentaAnalitica]></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-responsavel-tecnico\' | translate}}:</label></div><div align=center class=pdfEspacamentoLabels><table border=1 style=\"width: 100%\"><tr><td class=negritoTexto>{{\'pdf-cpf\' | translate}}</td><td class=negritoTexto>{{\'pdf-nome\' | translate}}</td><td class=negritoTexto>{{\'pdf-crea-cau\' | translate}}</td></tr><tr ng-repeat=\"responsaveltecnico in laudoRelatorio.identificacao.responsaveisTecnicos\"><td style=\"text-align: left\">{{responsaveltecnico.cpf | cpfcnpj}}</td><td style=\"text-align: left\">{{responsaveltecnico.nome}}</td><td style=\"text-align: left\">{{responsaveltecnico.crea}}</td></tr></table></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\"><label class=negritoTexto>{{\'pdf-data\' | translate}}:</label>&nbsp;<label ng-bind=\"dataAtual | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></div><div align=center class=\"pdfEspacamentoLabels negritoTexto\"><h3><u>{{\'pdf-resultados-avaliacao\' | translate}}</u></h3></div><div ng-if=\"laudoRelatorio.identificacao.metodoAvaliacao == \'COMPARATIVO_MERCADO\' || laudoRelatorio.identificacao.metodologia == \'INFERENCIA_ESTATISTICA\'\"><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.identificacao.metodoAvaliacao == \'COMPARATIVO_MERCADO\'\"><label class=negritoTexto>{{\'pdf-enquadramento-fundamentacao\' | translate}}:</label>&nbsp;<label>{{labelGrauFundamentacao}}</label></div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.identificacao.metodoAvaliacao == \'COMPARATIVO_MERCADO\'\"><label class=negritoTexto>{{\'pdf-enquadramento-precisao\' | translate}}:</label>&nbsp;<label>{{labelGrauPrecisao}}</label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-adotado\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(valorAdotado)></label>&nbsp;<label>({{convertNumeroPorExtenso(valorAdotado)}})</label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-minimo\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMinimo)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMinimo)}})</label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-medio\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMedio)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMedio)}})</label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-maximo\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMaximo)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMaximo)}})</label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\"><label class=negritoTexto>{{\'pdf-liquidez-do-imovel\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.liquidezImovel></label></div></div><div ng-if=\"laudoRelatorio.identificacao.metodologia == \'GLEBA_URBANIZAVEL\'\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-adotado\' | translate}} {{\'R$\'}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.valorTotalGleba)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.avaliacao.valorTotalGleba)}})</label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\"><label class=negritoTexto>{{\'pdf-liquidez-do-imovel\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.liquidezImovel></label></div></div><div ng-if=\"laudoRelatorio.identificacao.metodoAvaliacao == \'EVOLUTIVO\'\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-enquadramento-grau-fundamentacao-metodo-evolutivo\' | translate}}:</label>&nbsp;<label ng-show=enquadramentoGrauMetodoEvolutivo3>{{\'pdf-grau-3\' | translate}}</label><label ng-show=enquadramentoGrauMetodoEvolutivo2>{{\'pdf-grau-2\' | translate}}</label><label ng-show=enquadramentoGrauMetodoEvolutivo1>{{\'pdf-grau-1\' | translate}}</label></div></div><div ng-if=\"laudoRelatorio.identificacao.metodoAvaliacao == \'EVOLUTIVO\' || laudoRelatorio.identificacao.metodologia == \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\'\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-imovel\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.resultadoAvaliacao.valorImovel)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.resultadoAvaliacao.valorImovel)}})</label></div></div><div ng-if=\"laudoRelatorio.identificacao.metodologia == \'OUTRO\'\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-imovel\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.resultadosAvaliacao.valorAdotado)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.avaliacao.resultadosAvaliacao.valorAdotado)}})</label></div></div><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=1 number=imovelIdentificacaoNumber></contador-romano>- {{\'pdf-identificacao-imovel\' | translate}}</u></h3></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{imovelIdentificacaoNumber.value}}.<contador-romano order=1 parent=imovelIdentificacaoNumber></contador-romano>- {{\'pdf-localizacao-imovel\' | translate}}</i></div><div id=mapaLocalizacao align=center ng-if=carregarMapa><ci-mapa-avaliacao mostrar-amostras=false desabilitar=true imovel=imovelMapa></ci-mapa-avaliacao></div><div layout=row flex=100><div layout=row flex=30><label class=negritoTexto>{{\'pdf-pais\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.detalhesImovel.imovel.enderecamento.pais></label></div><div layout=row flex=30><label class=negritoTexto>{{\'pdf-uf\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.detalhesImovel.imovel.enderecamento.uf></label></div><div layout=row flex=30><label class=negritoTexto>{{\'pdf-municipio\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.detalhesImovel.imovel.enderecamento.municipio></label></div></div><div class=pdfEspacamentoCampos><label class=negritoTexto>{{\'pdf-endereco\' | translate}}:</label>&nbsp;<label ng-show=\"laudoRelatorio.detalhesImovel.imovel.enderecamento.pais == BRASIL\" ng-bind=\"laudoRelatorio.detalhesImovel.imovel.enderecamento.logradouro\n            + \', \' + (\'pdf-numero\' | translate) + \' \' + laudoRelatorio.detalhesImovel.imovel.enderecamento.numero\n            + \', \' + laudoRelatorio.detalhesImovel.imovel.enderecamento.bairro\n            + \', \' + laudoRelatorio.detalhesImovel.imovel.enderecamento.municipio + \'/\' + laudoRelatorio.detalhesImovel.imovel.enderecamento.uf\n            + \' - \' + laudoRelatorio.detalhesImovel.imovel.enderecamento.pais\n            + \'. \' + (\'pdf-cep\' | translate) + \': \' + (laudoRelatorio.detalhesImovel.imovel.enderecamento.cep | cep)\"></label><label ng-show=\"laudoRelatorio.detalhesImovel.imovel.enderecamento.pais != BRASIL\" ng-bind=\"laudoRelatorio.detalhesImovel.imovel.enderecamento.endereco\n            + \', \' + laudoRelatorio.detalhesImovel.imovel.enderecamento.cidadeExterior\n            + \' - \' + laudoRelatorio.detalhesImovel.imovel.enderecamento.pais\n            + \'. \' + (\'pdf-codigo-postal\' | translate) + \': \' + laudoRelatorio.detalhesImovel.imovel.enderecamento.codigoPostal\"></label></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\" ng-if=\"imovel.tipoImovel.descricao || laudoRelatorio.detalhesImovel.observacoesNaturezaImovel || imovel.observacoesGerais || imovel.naturezaImovel.descricao\"><i>{{imovelIdentificacaoNumber.value}}.<contador-romano order=2 parent=imovelIdentificacaoNumber></contador-romano>- {{\'pdf-detalhes-imovel\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=imovel.tipoImovel.descricao><label class=negritoTexto>{{\'pdf-tipo-imovel\' | translate}}:</label>&nbsp;<label ng-bind=imovel.tipoImovel.descricao></label></div><div class=pdfEspacamentoLabels ng-if=imovel.naturezaImovel.descricao><label class=negritoTexto>{{\'pdf-natureza-imovel\' | translate}}:</label>&nbsp;<label ng-bind=imovel.naturezaImovel.descricao></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-n-inscricao\' | translate}}:</label>&nbsp;<label ng-bind=imovel.numeroInscricao></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.detalhesImovel.observacoesNaturezaImovel><label class=negritoTexto>{{\'pdf-observacoes-natureza\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.detalhesImovel.observacoesNaturezaImovel></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=imovel.observacoesGerais><label class=negritoTexto>{{\'pdf-observacoes-gerais\' | translate}}:</label>&nbsp;<label ng-bind=imovel.observacoesGerais></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-n-processo\' | translate}}:</label>&nbsp;<label ng-bind=imovel.numeroProcesso></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-forma-incorporacao\' | translate}}:</label>&nbsp;<label ng-bind=imovel.formaIncorporacao></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-doacao-com-encargo\' | translate}}:</label>&nbsp;<label ng-bind=imovel.isDoacaoEncargo></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-encargo\' | translate}}:</label>&nbsp;<label ng-bind=imovel.valorEncargo></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-data-final-encargo\' | translate}}:</label>&nbsp;<label ng-bind=imovel.dataFinalEncargo></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-fundamento\' | translate}}:</label>&nbsp;<label ng-bind=imovel.fundamento></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-situacao-incorporacao\' | translate}}:</label>&nbsp;<label ng-bind=imovel.situacaoIncorporacao></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-direito-adquirido\' | translate}}:</label>&nbsp;<label ng-bind=imovel.formaIncorporacaoTipo></label></div><div ng-if=\"laudoRelatorio.detalhesImovel.imovel.matriculas.length > 0\"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{imovelIdentificacaoNumber.value}}.<contador-romano order=3 parent=imovelIdentificacaoNumber></contador-romano>- {{\'pdf-dados-cartoriais\' | translate}}</i></div><div class=pdfEspacamentoCampos><table border=1 style=\"width: 100%\"><tr><td class=negritoTexto>{{\'pdf-localizacao\' | translate}}</td><td class=negritoTexto>{{\'pdf-cartorio\' | translate}}</td><td class=negritoTexto>{{\'pdf-matricula-transcricao\' | translate}}</td><td class=negritoTexto>{{\'pdf-data-matricula\' | translate}}</td><td class=negritoTexto>{{\'pdf-data-registro\' | translate}}</td></tr><tr ng-repeat=\"matriculas in laudoRelatorio.detalhesImovel.imovel.matriculas\"><td style=\"text-align: left\"><label ng-bind=\"matriculas.uf + \' \' + matriculas.municipio\"></label></td><td style=\"text-align: left\"><label ng-bind=matriculas.nomeCartorio></label></td><td style=\"text-align: left\"><label ng-bind=matriculas.numero></label></td><td style=\"text-align: left\"><label ng-bind=\"matriculas.dataMatricula | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td style=\"text-align: left\"><label ng-bind=\"matriculas.dataRegistro | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td></tr></table></div></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{imovelIdentificacaoNumber.value}}.<contador-romano order=4 parent=imovelIdentificacaoNumber></contador-romano>- {{\'pdf-proprietario-imovel\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=dadosProprietario.tipoProprietarioReal.descricao><label class=negritoTexto>{{\'pdf-tipo-proprietario\' | translate}}:</label>&nbsp;<label ng-bind=dadosProprietario.tipoProprietarioReal.descricao></label></div><div class=pdfEspacamentoLabels ng-if=dadosProprietario.orgaoEntidade><label class=negritoTexto>{{\'pdf-orgao-entidade\' | translate}}:</label>&nbsp;<label ng-bind=dadosProprietario.orgaoEntidade></label></div><div class=pdfEspacamentoLabels ng-if=dadosProprietario.unidadeUsuario><label class=negritoTexto>{{\'pdf-ug-especifica\' | translate}}:</label>&nbsp;<label ng-bind=dadosProprietario.unidadeUsuario></label></div><div class=pdfEspacamentoCampos ng-if=\"(dadosProprietario.tipoProprietarioReal.descricao==TERCEIROS || dadosProprietario.tipoProprietarioParcial.descricao==TERCEIROS) && dadosProprietario.pessoas.length > 0\"><table border=1 style=\"width: 100%\"><tr><td class=negritoTexto>{{\'pdf-cpf-cnpj\' | translate}}</td><td class=negritoTexto>{{\'pdf-nome-razao-social\' | translate}}</td></tr><tr ng-repeat=\"pessoa in dadosProprietario.pessoas\"><td><label ng-bind=pessoa.cpf></label></td><td><label ng-bind=pessoa.nome></label></td></tr></table></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{imovelIdentificacaoNumber.value}}.<contador-romano order=5 parent=imovelIdentificacaoNumber></contador-romano>- {{\'pdf-descrição-regiao\' | translate}}</i></div><div class=pdfEspacamentoLabels><b>{{\'pdf-infraestrutura-urbana\' | translate}}:</b></div><md-container class=\"md-block espacamentoInputs\" flex=100 flex-xs=100 flex-sm=100><div layout=row flex=100><div flex=50 layout=column flex-offset=5><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isPavimentacao disabled name=\"{{\'pdf-pavimentacao\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isPavimentacao><label class=text-black>{{\'pdf-pavimentacao\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isIluminacaoPublica disabled name=\"{{\'pdf-iluminacao\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isIluminacaoPublica><label class=text-black>{{\'pdf-iluminacao\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isSarjetaGuia disabled name=\"{{\'pdf-sarjeta-guia\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isSarjetaGuia><label class=text-black>{{\'pdf-sarjeta-guia\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isRedeAgua disabled name=\"{{\'pdf-rede-agua\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isRedeAgua><label class=text-black>{{\'pdf-rede-agua\' | translate}}</label></md-checkbox></div><div flex=50 layout=column><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isRedeEletrica disabled name=\"{{\'pdf-rede-eletrica\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isRedeEletrica><label class=text-black>{{\'pdf-rede-eletrica\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isPasseio disabled name=\"{{\'pdf-passeio\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isPasseio><label class=text-black>{{\'pdf-passeio\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isRedeEsgoto disabled name=\"{{\'pdf-rede-esgoto\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isRedeEsgoto><label class=text-black>{{\'pdf-rede-esgoto\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isColetaLixo disabled name=\"{{\'pdf-coleta-de-lixo\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isColetaLixo><label class=text-black>{{\'pdf-coleta-de-lixo\' | translate}}</label></md-checkbox></div></div></md-container><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-descricao-outras-infraestruturas-urbanas\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.detalhesImovel.descricaoOutrosInfraestruturaUrbana></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-observacoes-infra-urbana\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.detalhesImovel.observacoesInfraestruturaUrbana></label></div><div class=pdfEspacamentoLabels><b>{{\'pdf-servicos-comunitarios\' | translate}}:</b></div><md-container class=\"md-block espacamentoInputs\" flex=100 flex-xs=100 flex-sm=100><div layout=row flex=100><div flex=50 layout=column flex-offset=5><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isEscola disabled name=\"{{\'pdf-escola\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isEscola><label class=text-black>{{\'pdf-escola\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isSeguranca disabled name=\"{{\'pdf-seguranca\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isSeguranca><label class=text-black>{{\'pdf-seguranca\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isComercio disabled name=\"{{\'pdf-comercio\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isComercio><label class=text-black>{{\'pdf-comercio\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isAreaLazer disabled name=\"{{\'pdf-area-lazer\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isAreaLazer><label class=text-black>{{\'pdf-area-lazer\' | translate}}</label></md-checkbox></div><div flex=50 layout=column><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isPostoSaude disabled name=\"{{\'pdf-posto-saude\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isPostoSaude><label class=text-black>{{\'pdf-posto-saude\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isTransporteColetivo disabled name=\"{{\'pdf-transporte-coletivo\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isTransporteColetivo><label class=text-black>{{\'pdf-transporte-coletivo\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isRedeBancaria disabled name=\"{{\'pdf-rede-bancaria\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isRedeBancaria><label class=text-black>{{\'pdf-rede-bancaria\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.detalhesImovel.isColetaLixoServicoComunitario disabled name=\"{{\'pdf-coleta-de-lixo\' | translate}}\" ng-checked=laudoRelatorio.detalhesImovel.isColetaLixoServicoComunitario><label class=text-black>{{\'pdf-coleta-de-lixo\' | translate}}</label></md-checkbox></div></div></md-container><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\"><label class=negritoTexto>{{\'pdf-descricao-servicos-comunitarios\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.detalhesImovel.descricaoOutrosServicosComunitarios></label></div><div ng-if=\"listaUtilizacaoAvaliacao.length > 0\"><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=2 number=utilizacaoNumber></contador-romano>- {{\'pdf-utilizacao\' | translate}}</u></h3></div><div ng-repeat=\"dadosUtilizacao in listaUtilizacaoAvaliacao\"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{utilizacaoNumber.value}}.<contador-romano order=1 parent=utilizacaoNumber></contador-romano>- {{\'pdf-dados-utilizacao\' | translate}}</i></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-instrumento-destinacao\' | translate}}:</label>&nbsp;<label ng-bind=dadosUtilizacao.destinacao.tipoDestinacao.descricao></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-tipo-uso\' | translate}}:</label>&nbsp;<label ng-bind=dadosUtilizacao.destinacao.utilizacao.tipoUtilizacao.descricao></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-especificacao\' | translate}}:</label>&nbsp;<label ng-bind=dadosUtilizacao.destinacao.utilizacao.subTipoUtilizacao.descricao></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\"><label class=negritoTexto>{{\'pdf-descricao\' | translate}}:</label>&nbsp;<label ng-bind=\"\"></label></div></div></div><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=3 number=terrenoNumber></contador-romano>- {{\'pdf-caracterizacao-terreno\' | translate}}</u></h3></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{terrenoNumber.value}}.<contador-romano order=1 parent=terrenoNumber></contador-romano>- {{\'pdf-area-terreno\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.terrenoImovel.terreno.areaTotal><label class=negritoTexto>{{\'pdf-area-total\' | translate}}:</label>&nbsp;<label ng-bind=\"formatarValorMonetario(laudoRelatorio.terrenoImovel.terreno.areaTotal) + \' \' + unidadeMedida\"></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.terrenoImovel.terreno.areaUniao><label class=negritoTexto>{{\'pdf-area-uniao\' | translate}}:</label>&nbsp;<label ng-bind=\"formatarValorMonetario(laudoRelatorio.terrenoImovel.terreno.areaUniao) + \' \' + unidadeMedida\"></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.terrenoImovel.terreno.fracaoIdealImovel><label class=negritoTexto>{{\'pdf-fracao-imovel\' | translate}}:</label>&nbsp;<label ng-bind=\"(laudoRelatorio.terrenoImovel.terreno.fracaoIdealImovel | currency).replace(\'R$\', \'\')\"></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=laudoRelatorio.terrenoImovel.terreno.fracaoIdealUniap><label class=negritoTexto>{{\'pdf-fracao-uniao\' | translate}}:</label>&nbsp;<label ng-bind=\"(laudoRelatorio.terrenoImovel.terreno.fracaoIdealUniao | currency).replace(\'R$\', \'\')\"></label></div><div class=\"negritoTexto pdfEspacamentoLabels\"><label class=negritoTexto>{{\'pdf-restricao-uso-ocupacao\' | translate}}:</label></div><div flex layout=column flex-offset=5><md-checkbox ng-disabled=true class=md-primary ng-model=terreno.isZonaInteresseSocial name=\"{{\'label-zona-area-interesse-social\' | translate}}\"><label class=text-black>{{\'pdf-zona-area-interesse-social\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=true class=md-primary ng-model=terreno.isSituadoAreaSeguranca name=\"{{\'pdf-situado-em-area-seguranca\' | translate}}\"><label class=text-black>{{\'pdf-situado-em-area-seguranca\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=true class=md-primary ng-model=terreno.isAreaNonAedificandi name=\"{{\'pdf-area-non-aedificandi\' | translate}}\"><label class=text-black>{{\'pdf-area-non-aedificandi\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=true class=\"md-primary with-field\" ng-model=terreno.isTombadoPatrimonioHistorico><label class=text-black>{{\'pdf-tombado-como-patrimonio-historico\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=true class=\"md-primary with-field\" ng-model=terreno.isOutraRestricaoUso><label class=text-black>{{\'pdf-outra-restricao-uso-ocupacao\' | translate}}</label></md-checkbox></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{terrenoNumber.value}}.<contador-romano order=2 parent=terrenoNumber></contador-romano>- {{\'pdf-descricao-carateristicas-fisicas\' | translate}}</i></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-dimensoes\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.terrenoImovel.dimensoes></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-nivel-rua\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.terrenoImovel.nivelRua></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-formato\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.terrenoImovel.formato></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-topografia\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.terrenoImovel.topografia></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\"><label class=negritoTexto>{{\'pdf-observacoes\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.terrenoImovel.observacoesCaracteristicas></label></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\" ng-if=\"laudoRelatorio.terrenoImovel.afastamentoLateral || laudoRelatorio.terrenoImovel.afastamentoFrontal || laudoRelatorio.terrenoImovel.gabarito || laudoRelatorio.terrenoImovel.taxaOcupacaoMaxima || laudoRelatorio.terrenoImovel.indiceAproveitamento || laudoRelatorio.terrenoImovel.observacoesParametrosUrbanisticos\"><i>{{terrenoNumber.value}}.<contador-romano order=3 parent=terrenoNumber></contador-romano>- {{\'pdf-parametros-urbanisticos\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.terrenoImovel.afastamentoLateral><label class=negritoTexto>{{\'pdf-afastamento-lateral\' | translate}}:</label>&nbsp;<label ng-bind=\"(laudoRelatorio.terrenoImovel.afastamentoLateral | currency).replace(\'R$\',\'\')\"></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.terrenoImovel.afastamentoFrontal><label class=negritoTexto>{{\'pdf-afastamento-frontal\' | translate}}:</label>&nbsp;<label ng-bind=\"(laudoRelatorio.terrenoImovel.afastamentoFrontal | currency).replace(\'R$\',\'\')\"></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.terrenoImovel.gabarito><label class=negritoTexto>{{\'pdf-gabarito\' | translate}}:</label>&nbsp;<label ng-bind=\"(laudoRelatorio.terrenoImovel.gabarito | currency).replace(\'R$\',\'\')\"></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.terrenoImovel.taxaOcupacaoMaxima><label class=negritoTexto>{{\'pdf-taxa-ocupacao-max\' | translate}}:</label>&nbsp;<label ng-bind=\"(laudoRelatorio.terrenoImovel.taxaOcupacaoMaxima | currency).replace(\'R$\',\'\')\"></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.terrenoImovel.indiceAproveitamento><label class=negritoTexto>{{\'pdf-indice-aproveitamento\' | translate}}:</label>&nbsp;<label ng-bind=\"(laudoRelatorio.terrenoImovel.indiceAproveitamento | currency).replace(\'R$\',\'\')\"></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=laudoRelatorio.terrenoImovel.observacoesParametrosUrbanisticos><label class=negritoTexto>{{\'pdf-observacoes\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.terrenoImovel.observacoesParametrosUrbanisticos></label></div><div ng-if=\"laudoRelatorio.benfeitorias.benfeitoriasAvaliacao && laudoRelatorio.benfeitorias.benfeitoriasAvaliacao.length > 0\"><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=4 number=benfeitoriasNumber></contador-romano>- {{\'pdf-benfeitorias\' | translate}}</u></h3></div><div ng-repeat=\"benfeitoriaAvaliacao in laudoRelatorio.benfeitorias.benfeitoriasAvaliacao track by $index\"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i><label>{{benfeitoriasNumber.value}}.<contador-romano order=$index parent=benfeitoriasNumber></contador-romano>- {{(\'title-benfeitoria\' | translate)}} {{benfeitoriaAvaliacao.benfeitoriaImovel.codigo + \' - \' + benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.descricao + \'/\' + (benfeitoriaAvaliacao.benfeitoriaImovel.edificacao ? benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoriaAvaliacao.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)}}</label></i></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.descricao><label class=negritoTexto>{{\'pdf-tipo-acessao-benfeitoria\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.descricao></label></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao><label class=negritoTexto>{{\'pdf-tipo-especializacao-edificacao\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-tipo-projeto\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoProjeto.descricao></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-descricao\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.descricao></label></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.benfeitoriaImovel.areaConstruida><label class=negritoTexto>{{\'pdf-area-construida\' | translate}}:</label>&nbsp;<label ng-bind=\"(benfeitoriaAvaliacao.benfeitoriaImovel.areaConstruida | currency).replace(\'R$\',\'\') + \' \' + unidadeMedida\"></label></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.areaProjecao><label class=negritoTexto>{{\'pdf-area-projecao\' | translate}}:</label>&nbsp;<label ng-bind=\"(benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.areaProjecao | currency).replace(\'R$\',\'\') + \' \' + unidadeMedida\"></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-taxa-ocupacao\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.taxaOcupacao></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-indice-aproveitamento\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.indiceAproveitamento></label></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.numeroPavimentos><label class=negritoTexto>{{\'pdf-numero-pavimentos\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.numeroPavimentos></label></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.numeroVagasCobertas><label class=negritoTexto>{{\'pdf-vagas-garagem-cobertas\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.numeroVagasCobertas></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-show=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.numeroVagasDescobertas><label class=negritoTexto>{{\'pdf-vagas-garagem-descobertas\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.numeroVagasDescobertas></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-possui-elevador\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.isPossuiElevador></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-possui-etiqueta-conservacao-energia\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.isPossuiEtiquetaConservacao></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-tipo\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.tipo></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-tipo-parcial\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.tipoParcial></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-nivel\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.benfeitoriaImovel.nivel></label></div><div ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.isVagaDeficienteAcessivel != null ||\n                                        benfeitoriaAvaliacao.benfeitoriaImovel.isAcessosInteriorAcessivel != null ||\n                                        benfeitoriaAvaliacao.benfeitoriaImovel.isElevadorAcessivel != null ||\n                                        benfeitoriaAvaliacao.benfeitoriaImovel.isBanheiroAcessivel != null \"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>◦ {{\'pdf-condicoes-acessibilidade\' | translate}}</i></div><div class=pdfEspacamentoLabels><table border=1 style=\"width: 100%\"><tr ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.isVagaDeficienteAcessivel != null\"><td style=\"text-align: left\">{{\'pdf-vagas-deficientes-acessiveis\' | translate}}</td><td>{{benfeitoriaAvaliacao.benfeitoriaImovel.isVagaDeficienteAcessivel ? \'Sim\' : \'Não\'}}</td></tr><tr ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.isAcessosInteriorAcessivel != null\"><td style=\"text-align: left\">{{\'pdf-acessos-interior-acessiveis\' | translate}}</td><td>{{benfeitoriaAvaliacao.benfeitoriaImovel.isAcessosInteriorAcessivel ? \'Sim\' : \'Não\'}}</td></tr><tr ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.isBanheiroAcessivel != null\"><td style=\"text-align: left\">{{\'pdf-banheiro-acessivel\' | translate}}</td><td>{{benfeitoriaAvaliacao.benfeitoriaImovel.isBanheiroAcessivel ? \'Sim\' : \'Não\'}}</td></tr><tr ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.isElevadorAcessivel != null\"><td style=\"text-align: left\">{{\'pdf-elevador-acessivel\' | translate}}</td><td>{{benfeitoriaAvaliacao.benfeitoriaImovel.isElevadorAcessivel ? \'Sim\' : \'Não\'}}</td></tr></table></div></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\" ng-show=\"benfeitoriaAvaliacao.divisaoInterna || benfeitoriaAvaliacao.materiaisConstrutivosInternos ||\n                                                                                           benfeitoriaAvaliacao.estrutura || benfeitoriaAvaliacao.cobertura ||\n                                                                                           benfeitoriaAvaliacao.fachada || benfeitoriaAvaliacao.instalacoesEquipamentos\"><i>◦ {{\'pdf-descricao-benfeitoria\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.divisaoInterna><label class=negritoTexto>{{\'pdf-divisao-interna\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.divisaoInterna></label></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.materiaisConstrutivosInternos><label class=negritoTexto>{{\'pdf-materiais-construtivos\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.materiaisConstrutivosInternos></label></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.estrutura><label class=negritoTexto>{{\'pdf-estrutura\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.estrutura></label></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.cobertura><label class=negritoTexto>{{\'pdf-cobertura\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.cobertura></label></div><div class=pdfEspacamentoLabels ng-show=benfeitoriaAvaliacao.fachada><label class=negritoTexto>{{\'pdf-fachada\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.fachada></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-show=benfeitoriaAvaliacao.instalacoesEquipamentos><label class=negritoTexto>{{\'pdf-instalacoes-equipamentos\' | translate}}:</label>&nbsp;<label ng-bind=benfeitoriaAvaliacao.instalacoesEquipamentos></label></div></div></div><div id=dadosunidadeAutonoma layout=column ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=4 number=unidadeAutonomaNumber></contador-romano>- {{\'pdf-unidade-autonoma\' | translate}}</u></h3></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{unidadeAutonomaNumber.value}}.<contador-romano order=1 parent=unidadeAutonomaNumber></contador-romano>- {{\'pdf-dados-edificacao\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.tipoEdificacao.descricao><label class=negritoTexto>{{\'pdf-tipo-edificacao\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.tipoEdificacao.descricao></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.possuiElevador><label class=negritoTexto>{{\'pdf-possui-elevador\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.possuiElevador></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.tipoProjeto.descricao><label class=negritoTexto>{{\'pdf-tipo-projeto\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.tipoProjeto.descricao></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.nome><label class=negritoTexto>{{\'pdf-nome\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.nome></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.observacoes><label class=negritoTexto>{{\'pdf-observacoes-gerais-edificacao\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.edificacao.observacoes></label></div><div ng-show=\"laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isVagaDeficienteAcessivel != null ||\n                                        laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isAcessosInteriorAcessivel != null ||\n                                        laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isElevadorAcessivel != null ||\n                                        laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isBanheiroAcessivel != null \"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>◦ {{\'pdf-condicoes-acessibilidade\' | translate}}</i></div><div class=pdfEspacamentoLabels><table border=1 style=\"width: 100%\"><tr ng-show=\"laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isVagaDeficienteAcessivel != null\"><td style=\"text-align: left\">{{\'pdf-vagas-deficientes-acessiveis\' | translate}}</td><td>{{laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isVagaDeficienteAcessivel ? \'Sim\' : \'Não\'}}</td></tr><tr ng-show=\"laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isAcessosInteriorAcessivel != null\"><td style=\"text-align: left\">{{\'pdf-acessos-interior-acessiveis\' | translate}}</td><td>{{laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isAcessosInteriorAcessivel ? \'Sim\' : \'Não\'}}</td></tr><tr ng-show=\"laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isBanheiroAcessivel != null\"><td style=\"text-align: left\">{{\'pdf-banheiro-acessivel\' | translate}}</td><td>{{laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isBanheiroAcessivel ? \'Sim\' : \'Não\'}}</td></tr><tr ng-show=\"laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isElevadorAcessivel != null\"><td style=\"text-align: left\">{{\'pdf-elevador-acessivel\' | translate}}</td><td>{{laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.condicoesAcessibilidade.isElevadorAcessivel ? \'Sim\' : \'Não\'}}</td></tr></table></div></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-detalhes-unidade-autonoma\' | translate}}</label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.area><label class=negritoTexto>{{\'pdf-area-construida\' | translate}}:</label>&nbsp;<label ng-bind=\"formatarValorMonetario(laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.area) + \' \' + unidadeMedida\"></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.numeroDormitorios><label class=negritoTexto>{{\'pdf-numero-dormitorios\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.numeroDormitorios></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.memorialDescritivo><label class=negritoTexto>{{\'pdf-memorial-descritivo\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.memorialDescritivo></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.observacoes><label class=negritoTexto>{{\'pdf-observacoes-gerais-unidade-autonoma\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.observacoes></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.numeroPavimentos><label class=negritoTexto>{{\'pdf-numero-pavimentos\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.numeroPavimentos></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.numeroVagasCobertas><label class=negritoTexto>{{\'pdf-vagas-garagem-cobertas\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.numeroVagasCobertas></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.numeroVagasDescobertas><label class=negritoTexto>{{\'pdf-vagas-garagem-descobertas\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.unidadeAutonomaIncorporacao.numeroVagasDescobertas></label></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\" ng-if=\"laudoRelatorio.unidadeAutonoma.divisaoInterna || laudoRelatorio.unidadeAutonoma.materiaisConstrutivosInternos || laudoRelatorio.unidadeAutonoma.estrutura || laudoRelatorio.unidadeAutonoma.cobertura || laudoRelatorio.unidadeAutonoma.fachada || laudoRelatorio.unidadeAutonoma.instalacoesEquipamentos\"><i>{{unidadeAutonomaNumber.value}}.<contador-romano order=2 parent=unidadeAutonomaNumber></contador-romano>- {{\'pdf-descricao-unidade-autonoma\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.divisaoInterna><label class=negritoTexto>{{\'pdf-divisao-interna\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.divisaoInterna></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.materiaisConstrutivosInternos><label class=negritoTexto>{{\'pdf-materiais-construtivos\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.materiaisConstrutivosInternos></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.estrutura><label class=negritoTexto>{{\'pdf-estrutura\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.estrutura></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.cobertura><label class=negritoTexto>{{\'pdf-cobertura\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.cobertura></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.unidadeAutonoma.fachada><label class=negritoTexto>{{\'pdf-fachada\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.fachada></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=laudoRelatorio.unidadeAutonoma.instalacoesEquipamentos><label class=negritoTexto>{{\'pdf-instalacoes-equipamentos\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.unidadeAutonoma.instalacoesEquipamentos></label></div></div><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=5 number=avaliacaoNumber></contador-romano>- {{\'pdf-vistoria\' | translate}}</u></h3></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.vistoria.dataVistoria><label class=negritoTexto>{{\'pdf-execucao-vistoria\' | translate}}:</label>&nbsp;<label>{{laudoRelatorio.vistoria.dataVistoria | date:\'dd/MM/yyyy\'}}</label></div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.vistoria.execucaoVistoria == \'VISTORIA_NAO_REALIZADA\'\"><label class=negritoTexto>{{\'pdf-execucao-vistoria\' | translate}}:</label>&nbsp;<label>{{listaExecucaoVistoria[laudoRelatorio.vistoria.execucaoVistoria]}}</label></div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.vistoria.execucaoVistoria != \'VISTORIA_NAO_REALIZADA\'\"><label class=negritoTexto>{{\'pdf-observacoes-vistoria\' | translate}}:</label>&nbsp;<label>{{laudoRelatorio.vistoria.observacao}}</label></div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.vistoria.execucaoVistoria == \'VISTORIA_NAO_REALIZADA\'\"><label class=negritoTexto>{{\'pdf-justificativa\' | translate}}:</label>&nbsp;<label>{{laudoRelatorio.vistoria.justificativa}}</label></div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.vistoria.execucaoVistoria != \'VISTORIA_NAO_REALIZADA\'\"><label>{{\'pdf-relatorio-fotografico-anexo\' | translate}}</label></div><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=5 number=avaliacaoNumber></contador-romano>- {{\'pdf-avaliacao\' | translate}}</u></h3></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{avaliacaoNumber.value}}.<contador-romano order=1 parent=avaliacaoNumber></contador-romano>- {{\'pdf-dados-avaliacao\' | translate}}</i></div><div ng-if=\"laudoRelatorio.identificacao.metodologia != \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\' && laudoRelatorio.identificacao.metodologia != \'GLEBA_URBANIZAVEL\' && laudoRelatorio.identificacao.metodologia != \'OUTRO\'\"><div class=pdfEspacamentoLabels><b>{{\'pdf-pressupostos\' | translate}}:</b></div><div class=\"pdfEspacamentoLabels pdfTab\"><i>• {{\'pdf-linearidade\' | translate}}:&nbsp;</i><label ng-bind=laudoRelatorio.avaliacao.linearidade></label><div align=center ng-repeat=\"imagem in linearidade\"><img ng-src=\"{{gerarImagem(imagem.arquivo.binario, imagem.arquivo.contentType)}}\" class=pdfMarginTop></div></div><div class=\"pdfEspacamentoLabels pdfTab\"><i>• {{\'pdf-normalidade\' | translate}}:&nbsp;</i><label ng-bind=laudoRelatorio.avaliacao.normalidade></label><div align=center ng-repeat=\"imagem in normalidade\"><img ng-src=\"{{gerarImagem(imagem.arquivo.binario, imagem2.arquivo.contentType)}}\" class=pdfMarginTop></div></div><div class=\"pdfEspacamentoLabels pdfTab\"><i>• {{\'pdf-homocedasticidade\' | translate}}:&nbsp;</i><label ng-bind=laudoRelatorio.avaliacao.homocedasticidade></label><div align=center ng-repeat=\"imagem in homocedasticidade\"><img ng-src=\"{{gerarImagem(imagem.arquivo.binario, imagem.arquivo.contentType)}}\" class=pdfMarginTop></div></div><div class=\"pdfEspacamentoLabels pdfTab\"><i>• {{\'pdf-verificacao-autocorrelacao\' | translate}}:&nbsp;</i><label ng-bind=laudoRelatorio.avaliacao.verificacaoAutocorrelacao></label><div align=center ng-repeat=\"imagem in verificacaoAutocorrelacao\"><img ng-src=\"{{gerarImagem(imagem.arquivo.binario, imagem.arquivo.contentType)}}\" class=pdfMarginTop></div></div><div class=\"pdfEspacamentoLabels pdfTab\"><i>• {{\'pdf-colinearidade-multicolinearidade\' | translate}}:&nbsp;</i><label ng-bind=laudoRelatorio.avaliacao.colinearidadeMulticolinearidade></label><div align=center ng-repeat=\"imagem in colinearidadeMulticolinearidade\"><img ng-src=\"{{gerarImagem(imagem.arquivo.binario, imagem.arquivo.contentType)}}\" class=pdfMarginTop></div></div><div class=\"pdfEspacamentoLabels pdfTab\"><i>• {{\'pdf-pontos-influenciantes\' | translate}}:&nbsp;</i><label ng-bind=laudoRelatorio.avaliacao.pontosInfluenciantes></label><div align=center ng-repeat=\"imagem in pontosInfluenciantes\"><img ng-src=\"{{gerarImagem(imagem.arquivo.binario, imagem.arquivo.contentType)}}\" class=pdfMarginTop></div></div></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.avaliacao.ressalvasFatoresLimitantes><label class=negritoTexto>{{\'pdf-ressalvas-fatores\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.ressalvasFatoresLimitantes></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.avaliacao.diagnosticoMercado><label class=negritoTexto>{{\'pdf-diagnostico-mercado\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.diagnosticoMercado></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.avaliacao.liquidezImovel><label class=negritoTexto>{{\'pdf-liquidez-imovel\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.liquidezImovel></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.avaliacao.pesquisaMercado><label class=negritoTexto>{{\'pdf-pesquisa-mercado\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.pesquisaMercado></label></div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.identificacao.ferramentaAnalitica && laudoRelatorio.identificacao.metodologia != \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\'\"><label class=negritoTexto ng-bind=\"(\'pdf-pressupostos-modelo\' | translate) + \' \' + ferramentasAnaliticas[laudoRelatorio.identificacao.ferramentaAnalitica]\"></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=laudoRelatorio.avaliacao.ferramentaUtilizada><label class=negritoTexto>{{\'pdf-ferramenta-utilizada\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.ferramentaUtilizada></label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=\"laudoRelatorio.identificacao.metodologia == \'OUTRO\'\"><label class=negritoTexto>{{\'label-descricao-metodologia-utilizada\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.descricao></label></div><div ng-if=\"laudoRelatorio.identificacao.metodologia == \'GLEBA_URBANIZAVEL\'\"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{avaliacaoNumber.value}}.<contador-romano order=1 parent=avaliacaoNumber></contador-romano>- <span ng-show=\"laudoRelatorio.avaliacao.modeloAvaliacaoGleba == \'HELIO_CAIRES\'\">{{\'pdf-helio-caires\' | translate}}</span> <span ng-show=\"laudoRelatorio.avaliacao.modeloAvaliacaoGleba == \'JOSE_BARBAGELATA\'\">{{\'pdf-jose-barbagelata\' | translate}}</span> <span ng-show=\"laudoRelatorio.avaliacao.modeloAvaliacaoGleba == \'OSCAR_OLAVE\'\">{{\'pdf-oscar-olave\' | translate}}</span></i></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-area-imovel\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.terrenoImovel.terreno.areaTotal | currency:\'\':4).replace(\'R$\', \'\')}}</label>&nbsp;</div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.avaliacao.modeloAvaliacaoGleba == \'JOSE_BARBAGELATA\' || laudoRelatorio.avaliacao.modeloAvaliacaoGleba == \'OSCAR_OLAVE\'\"><label class=negritoTexto>{{\'label-perdas-arruamento-lazer-areas-verdes\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.avaliacao.valorPerdasArruamento | currency).replace(\'R$\', \'\')}}</label>&nbsp;</div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-unitario\' | translate}}:</label>&nbsp;<label>{{listaValorUnitario[laudoRelatorio.avaliacao.tipoValorUnitario]}}</label>&nbsp;</div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.avaliacao.tipoValorUnitario == \'INFERENCIA_ESTATISTICA\'\"><label class=negritoTexto>{{\'pdf-lote-paradigma\' | translate}}:</label>&nbsp;<label>{{laudoRelatorio.avaliacao.loteParadigma}}</label>&nbsp;</div></div><div ng-if=\"(laudoRelatorio.identificacao.metodologia != \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\' && laudoRelatorio.identificacao.metodologia != \'OUTRO\' && laudoRelatorio.identificacao.metodologia != \'GLEBA_URBANIZAVEL\') || (laudoRelatorio.identificacao.metodologia == \'GLEBA_URBANIZAVEL\' && laudoRelatorio.avaliacao.tipoValorUnitario == \'INFERENCIA_ESTATISTICA\')\"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{avaliacaoNumber.value}}.<contador-romano order=2 parent=avaliacaoNumber></contador-romano>- {{\'pdf-variaveis\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-repeat=\"variavel in laudoRelatorio.avaliacao.variaveis\"><div class=pdfEspacamentoLabels><b>{{\'pdf-variavel\' | translate}} {{$index + 1}}</b></div><div class=pdfEspacamentoLabels><label>{{\'pdf-nome\' | translate}}:</label>&nbsp;<label ng-bind=variavel.nome></label></div><div class=pdfEspacamentoLabels><label>{{\'pdf-variavel\' | translate}}:</label>&nbsp;<label ng-bind=listaVariaveis[variavel.variavel]></label></div><div class=pdfEspacamentoLabels><label>{{\'pdf-tipo\' | translate}}:</label>&nbsp;<label ng-bind=listaTiposVariavel[variavel.tipoVariavel]></label></div><div class=pdfEspacamentoLabels><label>{{\'pdf-descricao\' | translate}}:</label>&nbsp;<label ng-bind=variavel.descricao></label></div><div class=pdfEspacamentoLabels><label>{{\'pdf-utilizada\' | translate}}:</label>&nbsp;<label>{{variavel.isVariavelUtilizadaModelo ? \'Sim\' : \'Não\'}}</label></div></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{avaliacaoNumber.value}}.<contador-romano order=3 parent=avaliacaoNumber></contador-romano>- {{\'pdf-amostras\' | translate}}</i></div><table border=1 style=\"width: 100%\" ng-repeat=\"amostra in amostras\"><tr><td><label class=negritoTexto>{{\'pdf-dado\' | translate}}</label><label>{{$index + 1}}</label></td><td><label class=negritoTexto>{{\'pdf-codigo\' | translate}}:</label><label ng-bind=amostra.codigoAmostra></label></td><td><label class=negritoTexto>{{\'pdf-utilizada\' | translate}}:</label><label>{{amostra.variaveisAmostras[0].isAmostraUtilizada ? \'Sim\' : \'Não\'}}</label></td><td><label class=negritoTexto>{{\'pdf-endereco\' | translate}}:</label><span ng-bind=\"amostra.enderecamento.logradouro + \', Número \'\n                                                        + amostra.enderecamento.numero + \', \'\n                                                        + amostra.enderecamento.bairro + \', \'\n                                                        + amostra.enderecamento.municipio + \'/\'\n                                                        + amostra.enderecamento.uf + \' - \'\n                                                        + (amostra.enderecamento.cep | cep)\"></span></td><td><label class=negritoTexto>{{\'pdf-informante-fonte\' | translate}}:</label><label ng-bind=amostra.informanteFonte></label></td></tr><tr ng-repeat=\"variavelAmostra in amostra.variaveisAmostras\"><td><label ng-bind=variavelAmostra.variavel.nome></label></td><td colspan=4><label ng-bind=variavelAmostra.valor></label></td></tr></table><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{avaliacaoNumber.value}}.<contador-romano order=4 parent=avaliacaoNumber></contador-romano>- {{\'pdf-atributos-avaliando\' | translate}}</i></div><table border=1 style=\"width: 100%\"><tr><td class=negritoTexto>{{\'pdf-variavel\' | translate}}</td><td class=negritoTexto>{{\'pdf-valor\' | translate}}</td><td class=negritoTexto>{{\'pdf-descricao\' | translate}}</td></tr><tr ng-repeat=\"variavel in laudoRelatorio.avaliacao.variaveis\"><td><label ng-bind=variavel.nome></label></td><td><label ng-bind=listaTiposVariavel[variavel.tipoVariavel]></label></td><td><label ng-bind=variavel.descricao></label></td></tr></table><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{avaliacaoNumber.value}}.<contador-romano order=5 parent=avaliacaoNumber></contador-romano>- {{\'pdf-localizacao-imovel-amostras\' | translate}}</i><div id=mapaAmostras align=center ng-if=carregarMapaAmostras><ci-mapa-avaliacao mostrar-amostras=true desabilitar=true imovel=imovelMapa mapa-variaveis-amostras=true latitude=amostras[0].latitude longitude=amostras[0].longitude></ci-mapa-avaliacao></div></div></div><div ng-if=\"laudoRelatorio.identificacao.metodologia == \'GLEBA_URBANIZAVEL\'\"><div ng-if=\"laudoRelatorio.avaliacao.tipoValorUnitario == \'INFERENCIA_ESTATISTICA\'\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-unitario-adotado\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.avaliacao.valorUnitarioAdotado | currency).replace(\'R$\', \'\')}}</label>&nbsp;</div></div><div ng-if=\"laudoRelatorio.avaliacao.tipoValorUnitario == \'DOMINIO_PLENO\'\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-fonte\' | translate}}:</label>&nbsp;<label>{{laudoRelatorio.avaliacao.dominioPlenoFonte}}</label>&nbsp;</div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-m\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.avaliacao.valorUnitarioAdotado | currency).replace(\'R$\', \'\')}}</label>&nbsp;</div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-data\' | translate}}:</label>&nbsp;<label>{{laudoRelatorio.avaliacao.dataDominioPleno | date : \'dd/MM/yyyy\'}}</label>&nbsp;</div></div><div ng-if=\"laudoRelatorio.avaliacao.tipoValorUnitario == \'OUTRA_METODOLOGIA\'\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-m\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.avaliacao.valorUnitarioAdotado | currency).replace(\'R$\', \'\')}}</label>&nbsp;</div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-descricao-metodologia\' | translate}}:</label>&nbsp;<label>{{laudoRelatorio.avaliacao.descricaoMetodologia}}</label>&nbsp;</div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-data\' | translate}}</label>&nbsp;<label>{{laudoRelatorio.avaliacao.dataOutraMetodologia | date : \'dd/MM/yyyy\'}}</label>&nbsp;</div></div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.avaliacao.modeloAvaliacaoGleba == \'OSCAR_OLAVE\'\"><label class=negritoTexto>{{\'pdf-lucro-empreendedor\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.avaliacao.lucroEmpregador | currency).replace(\'R$\', \'\')}}</label>&nbsp;</div><div class=pdfEspacamentoLabels><label class=negritoTexto ng-if=\"laudoRelatorio.avaliacao.modeloAvaliacaoGleba != \'JOSE_BARBAGELATA\'\">{{\'label-despesas-com-urbanizacao\' | translate}}:</label><label class=negritoTexto ng-if=\"laudoRelatorio.avaliacao.modeloAvaliacaoGleba == \'JOSE_BARBAGELATA\'\">{{\'label-despesas-unitaria-com-urbanizacao\' | translate}}:</label><label>{{(laudoRelatorio.avaliacao.despesasUrbanizacao | currency).replace(\'R$\', \'\')}}</label>&nbsp;</div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-fonte\' | translate}}:</label>&nbsp;<label>{{laudoRelatorio.avaliacao.descricaoFonte}}</label>&nbsp;</div></div><div ng-if=\"laudoRelatorio.identificacao.metodologia == \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\'\"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{avaliacaoNumber.value}}.<contador-romano order=2 parent=avaliacaoNumber></contador-romano>- {{\'pdf-estimativa-valor-benfeitorias\' | translate}}</i></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-fonte-valor-benfeitorias\' | translate}}:</label>&nbsp;<label ng-bind=tipoFonteValorBnfeitoria[laudoRelatorio.avaliacao.tipoValorLaudo]></label>&nbsp;</div><div ng-if=\"laudoRelatorio.avaliacao.tipoValorLaudo == \'OUTRA_FONTE\'\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-descricao-fonte\'| translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.descricaoFonte></label>&nbsp;</div><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'pdf-id\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'pdf-tipo\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'pdf-descricao\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'pdf-area-construida\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'pdf-valor-estimado\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in listaTerrenosParcela\"><td md-cell class=th-margin-pvgs>{{benfeitoria.id}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.tipo}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.areaConstruida | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorEdificacao.valor | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container></md-content></div></div><div ng-if=\"laudoRelatorio.avaliacao.tipoValorLaudo == \'CUB\'\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-area\' | translate}}:</label>&nbsp;<label ng-bind=listaTiposArea[laudoRelatorio.quantificacaoCusto.tipoArea]></label>&nbsp;</div><div layout=column ng-if=\"laudoRelatorio.quantificacaoCusto.tipoArea == \'AREA_REAL\'\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-cub\' | translate}}*</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.cubDepreciacao}}</td></tr></tbody></table></md-table-container></md-content></div><div layout=column ng-if=\"laudoRelatorio.quantificacaoCusto.tipoArea == \'AREA_EQUIVALENTE_CONSTRUCAO\'\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-area-equivalente\' | translate}}</th><th style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-cub-padrao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.areaEquivalente.areaEquivalenteConstrucao | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.areaEquivalente.cubPadrao}}</td></tr></tbody></table></md-table-container></md-content></div><div class=\"negritoTexto pdfEspacamentoLabels\"><i>{{avaliacaoNumber.value}}.<contador-romano order=2 parent=avaliacaoNumber></contador-romano>- {{\'pdf-estimacao-custos\' | translate}}</i></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-estimacao-custos\' | translate}}:</label>&nbsp;<label ng-bind=listaTiposEstimativaCusto[laudoRelatorio.quantificacaoCusto.tipoEstimativaCusto]></label>&nbsp;</div><div layout=column ng-if=laudoRelatorio.quantificacaoCusto.tipoEstimativaCusto><table md-table ng-if=laudoRelatorio.quantificacaoCusto.tipoEstimativaCusto><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 80%\" class=th-margin-pvgs md-column></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs ng-if=\"laudoRelatorio.quantificacaoCusto.tipoEstimativaCusto == \'UTILIZAR_ORCAMENTOS\'\"><table class=no-border md-table><tr class=no-border md-row><td class=no-border style=\"width: 40%\" md-cell>{{\'label-orcamento-elevadores\' | translate}}</td><td class=no-border style=\"width: 60%\" md-cell>{{(benfeitoria.valorOrcamentoElevadores | currency).replace(\'R$\', \'\')}}</td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-instalacoes-especiais\' | translate}}</td><td class=no-border md-cell>{{(benfeitoria.valorOrcamentoInstalacoesEspeciais | currency).replace(\'R$\', \'\')}}</td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-fundacoes-especiais\' | translate}}</td><td class=no-border md-cell>{{(benfeitoria.valorOrcamentoFundacoesEspeciais | currency).replace(\'R$\', \'\')}}</td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-fundacoes-diretas\' | translate}}</td><td class=no-border md-cell>{{(benfeitoria.valorOrcamentoFundacoesDiretas | currency).replace(\'R$\', \'\')}}</td></tr></table></td><td md-cell class=th-margin-pvgs ng-if=\"laudoRelatorio.quantificacaoCusto.tipoEstimativaCusto == \'APLICAR_PERCENTUAL\'\"><table class=no-border md-table><tr class=no-border md-row><td class=no-border style=\"width: 40%\" md-cell>{{\'label-elevadores\' | translate}}</td><td class=no-border style=\"width: 60%\" md-cell>{{(benfeitoria.valorOrcamentoElevadores | currency).replace(\'R$\', \'\')}}</td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-instalacoes-especiais\' | translate}}</td><td class=no-border md-cell>{{(benfeitoria.valorOrcamentoInstalacoesEspeciais | currency).replace(\'R$\', \'\')}}</td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-fundacoes-especiais\' | translate}}</td><td class=no-border md-cell>{{(benfeitoria.valorOrcamentoFundacoesEspeciais | currency).replace(\'R$\', \'\')}}</td></tr></table></td></tr></tbody></table></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{avaliacaoNumber.value}}.<contador-romano order=3 parent=avaliacaoNumber></contador-romano>- {{\'pdf-custo-construcao\' | translate}}</i></div><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-custo-construcao\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-estimativa\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorCustoConstrucao | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{((benfeitoria.area * benfeitoria.valorCustoConstrucao) | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container></md-content></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{avaliacaoNumber.value}}.<contador-romano order=4 parent=avaliacaoNumber></contador-romano>- {{\'pdf-bdi\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.tipoBdi><label class=negritoTexto>{{\'pdf-bdi\' | translate}}:</label>&nbsp;<label ng-bind=listaTiposDbi[laudoRelatorio.quantificacaoCusto.tipoBdi]></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.descricaoBdi><label class=negritoTexto>{{\'pdf-descricao-bdi-utilizado\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.quantificacaoCusto.descricaoBdi></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.valorTaxaBdi><label class=negritoTexto>{{\'pdf-taxa-bdi\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.quantificacaoCusto.valorTaxaBdi | currency).replace(\'R$\', \'\')}}</label></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{avaliacaoNumber.value}}.<contador-romano order=4 parent=avaliacaoNumber></contador-romano>- {{\'pdf-depreciacao\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.tipoDepreciacao><label class=negritoTexto>{{\'pdf-depreciacao\' | translate}}:</label>&nbsp;<label ng-bind=listaTiposDepreciacao[laudoRelatorio.quantificacaoCusto.tipoDepreciacao]></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.descricaoDepreciacao><label class=negritoTexto>{{\'pdf-descricao-depreciacao-calculada\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.quantificacaoCusto.descricaoDepreciacao></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.valorTaxaDepreciacao><label class=negritoTexto>{{\'pdf-taxa-depreciacao\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.quantificacaoCusto.valorTaxaDepreciacao | currency).replace(\'R$\', \'\')}}</label></div><div layout=column ng-if=\"laudoRelatorio.quantificacaoCusto.tipoDepreciacao == \'ROSS_HEIDECKE\'\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-vida-util\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-idade-aparente\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-taxa-depreciacao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.vidaUtil}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.idadeAparente}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.estadoConservacao.nome}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorTaxaDepreciacao | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container></md-content></div><div layout=row flex flex-xs ng-if=\"laudoRelatorio.quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><label class=textoAccordionPvgLaudo>{{\'label-informacoes-contabilidade\' | translate}}</label></div><div flex ng-if=\"laudoRelatorio.quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-vida-util\' | translate}}*</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-idade-aparente\' | translate}}*</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-cub\' | translate}}*</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.vidaUtil}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.idadeAparente}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.cubDepreciacao}}</td></tr></tbody></table></md-table-container></md-content></div></div><div class=\"negritoTexto pdfEspacamentoLabels\"><i>{{avaliacaoNumber.value}}.<contador-romano order=5 parent=avaliacaoNumber></contador-romano>- {{\'pdf-valor-benfeitorias\' | translate}}</i></div><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th colspan=2 style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-valor\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorCalculado | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{convertNumeroPorExtenso(benfeitoria.valorCalculado)}}</td></tr><tr md-row><td md-cell class=th-margin-pvgs>{{\'label-custo-total\' | translate}}</td><td md-cell class=th-margin-pvgs>{{(valorCustoTotalBenfeitoria | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{convertNumeroPorExtenso(valorCustoTotalBenfeitoria)}}</td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row flex flex-xs><label class=textoAccordionPvgLaudo>{{\'label-avaliacao-terreno\' | translate}}</label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-fonte\' | translate}}:</label>&nbsp;<label ng-show=\"laudoRelatorio.avaliacao.tipoValorTerrenoAvaliacao == \'PVG_MUNICIPIO\'\">{{\'radio-valor-municipio\' | translate}}</label><label ng-show=\"laudoRelatorio.avaliacao.tipoValorTerrenoAvaliacao == \'PVG_SPU\'\">{\'radio-pvg-spu\' | translate}}</label><label ng-show=\"laudoRelatorio.avaliacao.tipoValorTerrenoAvaliacao == \'PVG_INCRA\'\">{{\'radio-incra\' | translate}}</label><label ng-show=\"laudoRelatorio.avaliacao.tipoValorTerrenoAvaliacao == \'OUTRA_FONTE\'\">{{\'radio-outra-fonte\' | translate}}</label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-terreno\' | translate}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.valorTerreno)></label>&nbsp;</div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.avaliacao.tipoValorTerrenoAvaliacao == \'OUTRA_FONTE\'\"><label class=negritoTexto>{{\'pdf-valor-m2\' | translate}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.valorTerreno/laudoRelatorio.terrenoImovel.terreno.areaTotal)></label>&nbsp;</div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.avaliacao.tipoValorTerrenoAvaliacao != \'OUTRA_FONTE\'\"><label class=negritoTexto>{{\'pdf-valor-m2\' | translate}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(pvg.valor)></label>&nbsp;</div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.avaliacao.dataFonteValor><label class=negritoTexto>{{\'pdf-periodo-avaliacao\' | translate}}:</label>&nbsp;<label ng-bind=\"(laudoRelatorio.avaliacao.dataFonteValor | date:\'dd/MM/yyyy\')\"></label></div></div><div ng-if=\"laudoRelatorio.identificacao.metodologia == \'GLEBA_URBANIZAVEL\'\"><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=6 number=resultadosAvaliacaoNumber></contador-romano>- {{\'pdf-resultados-avaliacao\' | translate}}</u></h3></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-total-gleba\' | translate}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.valorTotalGleba)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.avaliacao.valorTotalGleba)}})</label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\"><label class=negritoTexto>{{\'pdf-encerramento\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.encerramento></label></div></div><div ng-if=\"laudoRelatorio.identificacao.metodologia != \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\'  && laudoRelatorio.identificacao.metodologia != \'GLEBA_URBANIZAVEL\'\"><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=7 number=resultadosAvaliacaoNumber></contador-romano>- {{\'pdf-resultados-avaliacao\' | translate}}</u></h3></div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.identificacao.metodologia != \'OUTRO\'\"><label class=negritoTexto>{{\'pdf-modelo-adotado\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.resultadosAvaliacao.modeloAdotado></label>&nbsp;</div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.identificacao.metodologia != \'OUTRO\'\"><label class=negritoTexto>{{\'pdf-valor-adotado\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(valorAdotado)></label>&nbsp;<label>({{convertNumeroPorExtenso(valorAdotado)}})</label></div><div class=pdfEspacamentoLabels ng-if=\"laudoRelatorio.identificacao.metodologia == \'OUTRO\'\"><label class=negritoTexto>{{\'pdf-valor-adotado\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.resultadosAvaliacao.valorAdotado)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.avaliacao.resultadosAvaliacao.valorAdotado)}})</label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.avaliacao.resultadosAvaliacao.justificativaValorAdotado><label class=negritoTexto>{{\'pdf-justificativa-valor-adotado\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.resultadosAvaliacao.justificativaValorAdotado></label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-minimo\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMinimo)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMinimo)}})</label></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-valor-medio\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMedio)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMedio)}})</label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\"><label class=negritoTexto>{{\'pdf-valor-maximo\' | translate}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}:</label>&nbsp;<label ng-bind=formatarValorMonetario(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMaximo)></label>&nbsp;<label>({{convertNumeroPorExtenso(laudoRelatorio.avaliacao.resultadosAvaliacao.valorMaximo)}})</label></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=\"laudoRelatorio.identificacao.metodologia == \'OUTRO\'\"><label class=negritoTexto>{{\'pdf-encerramento\' | translate}}</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.resultadosAvaliacao.encerramento></label>&nbsp;</div><div ng-if=\"laudoRelatorio.identificacao.metodologia != \'OUTRO\'\"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{resultadosAvaliacaoNumber.value}}.<contador-romano order=1 parent=resultadosAvaliacaoNumber></contador-romano>- {{\'pdf-enquadramento-laudo-fundamentacao\' | translate}}</i></div><div flex layout=column><md-checkbox class=md-primary ng-model=laudoRelatorio.avaliacao.enquadramento.isApresentacaoLaudoModalidade disabled name=\"{{\'title-checkbox1-laudo-mobilidade\' | translate}}\" ng-checked=laudoRelatorio.avaliacao.enquadramento.isApresentacaoLaudoModalidade><label class=text-black>{{\'title-checkbox1-laudo-mobilidade\' | translate}}</label></md-checkbox><md-checkbox class=md-primary disabled ng-model=laudoRelatorio.avaliacao.enquadramento.isApresentacaoAnaliseLaudo name=\"{{\'title-checkbox2-analise-modelo\' | translate}}\" ng-checked=laudoRelatorio.avaliacao.enquadramento.isApresentacaoAnaliseLaudo><label class=text-black>{{\'title-checkbox2-analise-modelo\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.avaliacao.enquadramento.isIdenticacaoCompletaEnderecos disabled name=\"{{\'title-checkbox3-idenfificacao-completa\' | translate}}\" ng-checked=laudoRelatorio.avaliacao.enquadramento.isIdenticacaoCompletaEnderecos><label class=text-black>{{\'title-checkbox3-idenfificacao-completa\' | translate}}</label></md-checkbox><md-checkbox class=md-primary ng-model=laudoRelatorio.avaliacao.enquadramento.isAdocaoEstimativaTendencia name=\"{{\'title-checkbox4-estimativa\' | translate}}\" disabled ng-checked=laudoRelatorio.avaliacao.enquadramento.isAdocaoEstimativaTendencia><label class=text-black>{{\'title-checkbox4-estimativa\' | translate}}</label></md-checkbox></div><enquadramento-laudo-grau-fundamentacao class=simple-table label-grau=labelGrauFundamentacao avaliacao=laudoRelatorio.avaliacao medir-grau-enquadramento-fundamentacao=medirGrauEnquadramentoFundamentacao is-assinar=isAssinar enquadramento-grau3=enquadramentoGrau3 enquadramento-grau2=enquadramentoGrau2 enquadramento-grau1=enquadramentoGrau1 enquadramento-grau-nao-atingido=enquadramentoGrauNaoAtingido></enquadramento-laudo-grau-fundamentacao><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=laudoRelatorio.avaliacao.enquadramento.justificativaNaoEnquadramento><label class=negritoTexto>{{\'pdf-justificativa-nao-atingimento\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.enquadramento.justificativaNaoEnquadramento></label></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{resultadosAvaliacaoNumber.value}}.<contador-romano order=2 parent=resultadosAvaliacaoNumber></contador-romano>- {{\'pdf-enquadramento-laudo-precisao\' | translate}}</i></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-amplitude-intervalo-confianca\' | translate}}:</label>&nbsp;<label ng-bind=\"(laudoRelatorio.avaliacao.enquadramento.amplitudeIntervalo | currency).replace(\'R$\',\'\') + \'%\'\"></label></div><enquadramento-laudo-grau-precisao class=\"simple-table esconde-titulo\" label-grau=labelGrauPrecisao avaliacao=laudoRelatorio.avaliacao is-assinar=isAssinar></enquadramento-laudo-grau-precisao><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" ng-if=laudoRelatorio.avaliacao.enquadramento.justificativaNaoAtingimento><label class=negritoTexto>{{\'pdf-justificativa-nao-enquadramento-laudo\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.enquadramento.justificativaNaoAtingimento></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.avaliacao.encerramento><label class=negritoTexto>{{\'pdf-encerramento\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.avaliacao.encerramento></label></div></div><div ng-if=\"laudoRelatorio.identificacao.metodoAvaliacao == \'COMPARATIVO_MERCADO\' || laudoRelatorio.identificacao.metodologia == \'OUTRO\'\"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\" ng-if=\"listaTerrenosParcela.length > 0\"><i>{{resultadosAvaliacaoNumber.value}}.<contador-romano order=3 parent=resultadosAvaliacaoNumber></contador-romano>- {{\'pdf-estimativa-parcelas\' | translate}}</i></div><div ng-if=\"listaTerrenosParcela.length > 0\" class=pdfEspacamentoLabels>{{\'pdf-valor-estimado-terreno\' | translate}}:</div><div class=pdfEspacamentoCampos ng-if=\"listaTerrenosParcela.length > 0\"><table border=1 style=\"width: 100%\"><tr><td class=negritoTexto>{{\'pdf-id\' | translate}}</td><td class=negritoTexto>{{\'pdf-tipo\' | translate}}</td><td class=negritoTexto>{{\'pdf-descricao\' | translate}}</td><td class=negritoTexto>{{(\'pdf-area-construida\' | translate) + \' \' + unidadeMedida}}</td><td class=negritoTexto ng-if=!isMotivosEstimativa>{{\'pdf-vida-util\' | translate}}</td><td class=negritoTexto ng-if=!isMotivosEstimativa>{{\'pdf-idade-aparente\' | translate}}</td><td class=negritoTexto>{{\'pdf-cub\' | translate}}</td><td class=negritoTexto>{{(\'pdf-valor\' | translate)}} {{laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo}}</td></tr><tr ng-repeat=\"terrenoParcela in listaTerrenosParcela\"><td>{{terrenoParcela.id}}</td><td>{{terrenoParcela.tipo}}</td><td>{{terrenoParcela.descricao}}</td><td>{{(terrenoParcela.areaConstruida | currency).replace(\'R$\',\'\')}}</td><td ng-if=!isMotivosEstimativa>{{terrenoParcela.vidaUtil}}</td><td ng-if=!isMotivosEstimativa>{{terrenoParcela.idadeAparente}}</td><td ng-if=!isMotivosEstimativa>{{terrenoParcela.tipoProjeto + \' - Padrão \' + terrenoParcela.padraoAcabamento}}</td><td ng-if=isMotivosEstimativa><table border=1 width=100% style=\"border-coIIapse: coIIapse\"><tr><td colspan=2>{{\'pdf-cub\' | translate}}</td><td colspan=2>{{terrenoParcela.tipoProjeto + \' - Padrão \' + terrenoParcela.padraoAcabamento}}</td></tr><tr><td colspan=2>{{\'pdf-vida-util\' | translate}}</td><td colspan=2>{{terrenoParcela.vidaUtil}}</td></tr><tr><td colspan=2>{{\'pdf-idade-aparente\' | translate}}</td><td colspan=2>{{terrenoParcela.idadeAparente}}</td></tr><tr><td colspan=2>{{\'pdf-estado-conservacao\' | translate}}</td><td colspan=2>{{terrenoParcela.estadoConservacao.nome}}</td></tr><tr><td colspan=4></td></tr><tr><td class=negritoTexto>{{\'pdf-fator\' | translate}}</td><td class=negritoTexto>{{\'pdf-especificacao\' | translate}}</td><td class=negritoTexto>{{\'pdf-porcentagem\' | translate}}</td><td class=negritoTexto>{{\'pdf-justificativa-fonte\' | translate}}</td></tr><tr ng-repeat=\"estimativa in terrenoParcela.estimativas\"><td>{{listaMotivosEstimativa[estimativa.motivo]}}</td><td>{{estimativa.especificacao}}</td><td>{{(estimativa.porcentagem | currency).replace(\'R$\',\'\') + \'%\'}}</td><td>{{estimativa.justificativaFonte}}</td></tr><tr><td colspan=4></td></tr><tr><td colspan=2>{{\'pdf-valor-estimado-cub-m2\' | translate}}</td><td colspan=2>{{(terrenoParcela.valor | currency).replace(\'R$\',\'\')}}</td></tr></table></td><td>{{(terrenoParcela.valor | currency).replace(\'R$\',\'\')}}</td></tr></table></div></div></div><div ng-if=\"laudoRelatorio.identificacao.metodoAvaliacao == \'EVOLUTIVO\'\"><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=8 number=quantificacaoNumber></contador-romano>- {{\'pdf-quantificacao-custo\' | translate}}</u></h3></div><div ng-if=\"laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria == \'ORCAMENTO\'\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-descricao-orcamento\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.quantificacaoCusto.descricaoOrcamento></label>&nbsp;</div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{quantificacaoNumber.value}}.<contador-romano order=1 parent=quantificacaoNumber></contador-romano>- {{\'pdf-custo-reproducao\' | translate}}</i></div><div layout=column ng-if=\"laudoRelatorio.quantificacaoCusto.tipoArea == \'AREA_REAL\'\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th colspan=2 style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-valor\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorCustoReproducao | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{convertNumeroPorExtenso(benfeitoria.valorCustoReproducao)}}</td></tr></tbody></table></md-table-container></md-content></div></div><div ng-if=\"laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria == \'CUB_PROJETO_SEMELHANTE\'\"><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{quantificacaoNumber.value}}.<contador-romano order=1 parent=quantificacaoNumber></contador-romano>- {{\'pdf-area\' | translate}}</i></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-area\' | translate}}:</label>&nbsp;<label ng-bind=listaTiposArea[laudoRelatorio.quantificacaoCusto.tipoArea]></label>&nbsp;</div><div layout=column ng-if=\"laudoRelatorio.quantificacaoCusto.tipoArea == \'AREA_REAL\'\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-cub\' | translate}}*</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.cubDepreciacao}}</td></tr></tbody></table></md-table-container></md-content></div><div layout=column ng-if=\"laudoRelatorio.quantificacaoCusto.tipoArea == \'AREA_EQUIVALENTE_CONSTRUCAO\'\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-area-equivalente\' | translate}}</th><th style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-cub-padrao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.areaEquivalente.areaEquivalenteConstrucao | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.areaEquivalente.cubPadrao}}</td></tr></tbody></table></md-table-container></md-content></div><div class=\"negritoTexto pdfEspacamentoLabels\"><i>{{quantificacaoNumber.value}}.<contador-romano order=2 parent=quantificacaoNumber></contador-romano>- {{\'pdf-estimacao-custos\' | translate}}</i></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'pdf-estimacao-custos\' | translate}}:</label>&nbsp;<label ng-bind=listaTiposEstimativaCusto[laudoRelatorio.quantificacaoCusto.tipoEstimativaCusto]></label>&nbsp;</div><div layout=column ng-if=laudoRelatorio.quantificacaoCusto.tipoEstimativaCusto><table md-table ng-if=laudoRelatorio.quantificacaoCusto.tipoEstimativaCusto><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 80%\" class=th-margin-pvgs md-column></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs ng-if=\"laudoRelatorio.quantificacaoCusto.tipoEstimativaCusto == \'UTILIZAR_ORCAMENTOS\'\"><table class=no-border md-table><tr class=no-border md-row><td class=no-border style=\"width: 40%\" md-cell>{{\'label-orcamento-elevadores\' | translate}}</td><td class=no-border style=\"width: 60%\" md-cell>{{(benfeitoria.valorOrcamentoElevadores | currency).replace(\'R$\', \'\')}}</td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-instalacoes-especiais\' | translate}}</td><td class=no-border md-cell>{{(benfeitoria.valorOrcamentoInstalacoesEspeciais | currency).replace(\'R$\', \'\')}}</td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-fundacoes-especiais\' | translate}}</td><td class=no-border md-cell>{{(benfeitoria.valorOrcamentoFundacoesEspeciais | currency).replace(\'R$\', \'\')}}</td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-fundacoes-diretas\' | translate}}</td><td class=no-border md-cell>{{(benfeitoria.valorOrcamentoFundacoesDiretas | currency).replace(\'R$\', \'\')}}</td></tr></table></td><td md-cell class=th-margin-pvgs ng-if=\"laudoRelatorio.quantificacaoCusto.tipoEstimativaCusto == \'APLICAR_PERCENTUAL\'\"><table class=no-border md-table><tr class=no-border md-row><td class=no-border style=\"width: 40%\" md-cell>{{\'label-elevadores\' | translate}}</td><td class=no-border style=\"width: 60%\" md-cell>{{(benfeitoria.valorOrcamentoElevadores | currency).replace(\'R$\', \'\')}}</td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-instalacoes-especiais\' | translate}}</td><td class=no-border md-cell>{{(benfeitoria.valorOrcamentoInstalacoesEspeciais | currency).replace(\'R$\', \'\')}}</td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-fundacoes-especiais\' | translate}}</td><td class=no-border md-cell>{{(benfeitoria.valorOrcamentoFundacoesEspeciais | currency).replace(\'R$\', \'\')}}</td></tr></table></td></tr></tbody></table></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{quantificacaoNumber.value}}.<contador-romano order=3 parent=quantificacaoNumber></contador-romano>- {{\'pdf-custo-construcao\' | translate}}</i></div><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-custo-construcao\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-estimativa\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorCustoConstrucao | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{((benfeitoria.area * benfeitoria.valorCustoConstrucao) | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container></md-content></div></div><div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{quantificacaoNumber.value}}.<contador-romano order=4 parent=quantificacaoNumber></contador-romano>- {{\'pdf-bdi\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.tipoBdi><label class=negritoTexto>{{\'pdf-bdi\' | translate}}:</label>&nbsp;<label ng-bind=listaTiposDbi[laudoRelatorio.quantificacaoCusto.tipoBdi]></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.descricaoBdi><label class=negritoTexto>{{\'pdf-descricao-bdi-utilizado\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.quantificacaoCusto.descricaoBdi></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.valorTaxaBdi><label class=negritoTexto>{{\'pdf-taxa-bdi\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.quantificacaoCusto.valorTaxaBdi | currency).replace(\'R$\', \'\')}}</label></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{quantificacaoNumber.value}}.<contador-romano order=4 parent=quantificacaoNumber></contador-romano>- {{\'pdf-depreciacao\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.tipoDepreciacao><label class=negritoTexto>{{\'pdf-depreciacao\' | translate}}:</label>&nbsp;<label ng-bind=listaTiposDepreciacao[laudoRelatorio.quantificacaoCusto.tipoDepreciacao]></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.descricaoDepreciacao><label class=negritoTexto>{{\'pdf-descricao-depreciacao-calculada\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.quantificacaoCusto.descricaoDepreciacao></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.quantificacaoCusto.valorTaxaDepreciacao><label class=negritoTexto>{{\'pdf-taxa-depreciacao\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.quantificacaoCusto.valorTaxaDepreciacao | currency).replace(\'R$\', \'\')}}</label></div><div layout=column ng-if=\"laudoRelatorio.quantificacaoCusto.tipoDepreciacao == \'ROSS_HEIDECKE\'\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-vida-util\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-idade-aparente\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-taxa-depreciacao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.vidaUtil}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.idadeAparente}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.estadoConservacao.nome}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorTaxaDepreciacao | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container></md-content></div><div layout=row flex flex-xs ng-if=\"laudoRelatorio.quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><label class=textoAccordionPvgLaudo>{{\'label-informacoes-contabilidade\' | translate}}</label></div><div flex ng-if=\"laudoRelatorio.quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-vida-util\' | translate}}*</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-idade-aparente\' | translate}}*</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-cub\' | translate}}*</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.vidaUtil}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.idadeAparente}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.cubDepreciacao}}</td></tr></tbody></table></md-table-container></md-content></div></div><div class=\"negritoTexto pdfEspacamentoLabels\"><i>{{quantificacaoNumber.value}}.<contador-romano order=5 parent=quantificacaoNumber></contador-romano>- {{\'pdf-valor-benfeitorias\' | translate}}</i></div><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th colspan=2 style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-valor\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorCalculado | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{convertNumeroPorExtenso(benfeitoria.valorCalculado)}}</td></tr><tr md-row><td md-cell class=th-margin-pvgs>{{\'label-custo-total\' | translate}}</td><td md-cell class=th-margin-pvgs>{{(valorCustoTotalBenfeitoria | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{convertNumeroPorExtenso(valorCustoTotalBenfeitoria)}}</td></tr></tbody></table></md-table-container></md-content></div></div><div class=m-t-20 layout=row layout-xs=column><label class=textoAccordionPvgLaudo>{{\'title-grau-fundamentacao-metodo-quantificacao-custos-benfeitorias\' | translate}}</label></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=10% md-column>{{\'title-item\' | translate}}</th><th width=15% md-column>{{\'title-descricao\' | translate}}</th><th width=25% md-column>{{\'title-grau-3\' | translate}}</th><th width=25% md-column>{{\'title-grau-2\' | translate}}</th><th width=25% md-column>{{\'title-grau-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>1</label></td><td md-cell><label>{{\'label-estimativa-custo-direto\' | translate}}</label></td><td md-cell ng-style=\"laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria == \'ORCAMENTO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custo-direto-grau-3\' | translate}}</label></td><td md-cell ng-style=\"laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria == \'CUB_PROJETO_SEMELHANTE\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custo-direto-grau-2\' | translate}}</label></td><td md-cell ng-style=\"laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria == \'CUB_PROJETO_DIFERENTE\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custo-direto-grau-1\' | translate}}</label></td></tr><tr md-row><td md-cell><label>2</label></td><td md-cell><label>{{\'label-bdi\' | translate}}</label></td><td md-cell ng-style=\"laudoRelatorio.quantificacaoCusto.tipoBdi == \'CALCULADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-bdi-grau-3\' | translate}}</label></td><td md-cell ng-style=\"laudoRelatorio.quantificacaoCusto.tipoBdi == \'JUSTIFICADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-bdi-grau-2\' | translate}}</label></td><td md-cell ng-style=\"laudoRelatorio.quantificacaoCusto.tipoBdi == \'ARBITRADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-bdi-grau-1\' | translate}}</label></td></tr><tr md-row><td md-cell><label>3</label></td><td md-cell><label>{{\'label-depreciacao-fisica\' | translate}}</label></td><td md-cell ng-style=\"laudoRelatorio.quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-depreciacao-fisica-grau-3\' | translate}}</label></td><td md-cell ng-style=\"laudoRelatorio.quantificacaoCusto.tipoDepreciacao == \'ROSS_HEIDECKE\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-depreciacao-fisica-grau-2\' | translate}}</label></td><td md-cell ng-style=\"laudoRelatorio.quantificacaoCusto.tipoDepreciacao == \'ARBITRADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-depreciacao-fisica-grau-1\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row layout-xs=column><label class=textoAccordionPvgLaudo>{{\'title-enquadramento-grau-fundamentacao\' | translate}}</label></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=25% md-column>{{\'title-graus\' | translate}}</th><th width=25% md-column>{{\'title-3\' | translate}}</th><th width=25% md-column>{{\'title-2\' | translate}}</th><th width=25% md-column>{{\'title-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>{{\'label-pontos-minimos\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrauMetodoEvolutivo3 ? colorTableColumnAvaliacao : {}\"><label>7</label></td><td md-cell ng-style=\"enquadramentoGrauMetodoEvolutivo2 ? colorTableColumnAvaliacao : {}\"><label>5</label></td><td md-cell ng-style=\"enquadramentoGrauMetodoEvolutivo1 ? colorTableColumnAvaliacao : {}\"><label>3</label></td></tr><tr md-row><td md-cell><label>{{\'label-itens-obrigatorios-grau-correspondente\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-3\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-2\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-1\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div class=\"pdfEspacamentoLabels m-t-10\"><label>{{\'title-grau-fundamentacao-metodo-quantificacao-custos\' | translate}}</label>&nbsp;<label class=negritoTexto ng-show=enquadramentoGrauMetodoEvolutivo3>{{\'title-grau-3\' | translate}}</label><label class=negritoTexto ng-show=enquadramentoGrauMetodoEvolutivo2>{{\'title-grau-2\' | translate}}</label><label class=negritoTexto ng-show=enquadramentoGrauMetodoEvolutivo1>{{\'title-grau-1\' | translate}}</label></div></div><div align=left ng-if=\"laudoRelatorio.identificacao.metodoAvaliacao == \'EVOLUTIVO\' || laudoRelatorio.identificacao.metodologia == \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\'\"><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=9 number=resultadoAvaliacaoNumber></contador-romano>- {{\'pdf-resultado-avaliacao\' | translate}}</u></h3></div><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'pdf-terreno-benfeitoria\' | translate}}</th><th colspan=2 style=\"width: 80%\" class=th-margin-pvgs md-column>{{\'label-valor\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell class=th-margin-pvgs>{{\'pdf-terreno\' | translate}}</td><td md-cell class=th-margin-pvgs>{{(valorTerreno | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{convertNumeroPorExtenso(valorTerreno)}}</td></tr><tr md-row ng-repeat=\"benfeitoria in laudoRelatorio.quantificacaoCusto.benfeitorias\" ng-if=\"laudoRelatorio.avaliacao.tipoValorLaudo != \'OUTRA_FONTE\'\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorCalculado | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{convertNumeroPorExtenso(benfeitoria.valorCalculado)}}</td></tr><tr md-row ng-repeat=\"benfeitoria in listaTerrenosParcela\" ng-if=\"laudoRelatorio.avaliacao.tipoValorLaudo == \'OUTRA_FONTE\'\"><td md-cell class=th-margin-pvgs>{{benfeitoria.id + \'-\' + benfeitoria.tipo}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorEdificacao.valor | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{convertNumeroPorExtenso(benfeitoria.valorEdificacao.valor)}}</td></tr></tbody></table></md-table-container></md-content></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{resultadoAvaliacaoNumber.value}}.<contador-romano order=1 parent=resultadoAvaliacaoNumber></contador-romano>- {{\'pdf-fator-comercializacao\' | translate}}</i></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.resultadoAvaliacao.tipoFatorComercializacao><label class=negritoTexto>{{\'pdf-fator-comercializacao\' | translate}}:</label>&nbsp;<label ng-bind=listaTiposFatorComercializacao[laudoRelatorio.resultadoAvaliacao.tipoFatorComercializacao]></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.resultadoAvaliacao.descricaoFatorComercializacao><label class=negritoTexto>{{\'pdf-descricao-fator-comercializacao\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.resultadoAvaliacao.descricaoFatorComercializacao></label></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.resultadoAvaliacao.valorFc><label class=negritoTexto>{{\'pdf-fc\' | translate}}:</label>&nbsp;<label>{{(laudoRelatorio.resultadoAvaliacao.valorFc | currency).replace(\'R$\', \'\')}}</label></div><div class=\"negritoTexto pdfEspacamentoLabels pdfTab\"><i>{{resultadoAvaliacaoNumber.value}}.<contador-romano order=2 parent=resultadoAvaliacaoNumber></contador-romano>- {{\'pdf-valor-imovel\' | translate}}{{\': \' + laudoRelatorio.avaliacao.resultadosAvaliacao.moeda.simbolo + \' \'}} {{(laudoRelatorio.resultadoAvaliacao.valorImovel | currency).replace(\'R$\', \'\') + \' - \'}} {{convertNumeroPorExtenso(laudoRelatorio.resultadoAvaliacao.valorImovel)}}</i></div><div ng-if=\"laudoRelatorio.identificacao.metodoAvaliacao == \'EVOLUTIVO\'\"><div class=m-t-20 layout=row layout-xs=column><label class=textoAccordionPvgLaudo>{{\'title-grau-fundamentacao-metodo-evolutivo\' | translate}}</label></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=10% md-column>{{\'title-item\' | translate}}</th><th width=15% md-column>{{\'title-descricao\' | translate}}</th><th width=25% md-column>{{\'title-grau-3\' | translate}}</th><th width=25% md-column>{{\'title-grau-2\' | translate}}</th><th width=25% md-column>{{\'title-grau-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>1</label></td><td md-cell><label>{{\'label-estimativa-valor-terreno\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaValorTerrenoGrau3 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-valor-terreno-grau-3\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaValorTerrenoGrau2 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-valor-terreno-grau-2\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaValorTerrenoGrau1 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-valor-terreno-grau-1\' | translate}}</label></td></tr><tr md-row><td md-cell><label>2</label></td><td md-cell><label>{{\'label-estimativa-custos-reedicao\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaCustosReedicaoGrau3 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custos-reedicao-grau-3\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaCustosReedicaoGrau2 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custos-reedicao-grau-2\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaCustosReedicaoGrau1 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custos-reedicao-grau-1\' | translate}}</label></td></tr><tr md-row><td md-cell><label>3</label></td><td md-cell><label>{{\'label-fator-comercializacao\' | translate}}</label></td><td md-cell ng-style=\"itemFatorComercializacaoGrau3 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-fator-comercializacao-grau-3\' | translate}}</label></td><td md-cell ng-style=\"itemFatorComercializacaoGrau2 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-fator-comercializacao-grau-2\' | translate}}</label></td><td md-cell ng-style=\"itemFatorComercializacaoGrau1 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-fator-comercializacao-grau-1\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row layout-xs=column><label class=textoAccordionPvgLaudo>{{\'title-enquadramento-grau-fundamentacao\' | translate}}</label></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=25% md-column>{{\'title-graus\' | translate}}</th><th width=25% md-column>{{\'title-3\' | translate}}</th><th width=25% md-column>{{\'title-2\' | translate}}</th><th width=25% md-column>{{\'title-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>{{\'label-pontos-minimos\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrau3 ? colorTableColumnAvaliacao : {}\"><label>8</label></td><td md-cell ng-style=\"enquadramentoGrau2 ? colorTableColumnAvaliacao : {}\"><label>5</label></td><td md-cell ng-style=\"enquadramentoGrau1 ? colorTableColumnAvaliacao : {}\"><label>3</label></td></tr><tr md-row><td md-cell><label>{{\'label-itens-obrigatorios-grau-correspondente\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-3-resultado-avaliacao\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-2\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-1\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div class=\"pdfEspacamentoLabels m-t-10\"><label class=negritoTexto>{{\'title-grau-fundamentacao-laudo-avaliacao\' | translate}}:</label>&nbsp;<label ng-show=enquadramentoGrau3>{{\'title-grau-3\' | translate}}</label><label ng-show=enquadramentoGrau2>{{\'title-grau-2\' | translate}}</label><label ng-show=enquadramentoGrau1>{{\'title-grau-1\' | translate}}</label></div></div><div class=pdfEspacamentoLabels ng-if=laudoRelatorio.resultadoAvaliacao.encerramento><label class=negritoTexto>{{\'pdf-encerramento\' | translate}}:</label>&nbsp;<label ng-bind=laudoRelatorio.resultadoAvaliacao.encerramento></label></div></div><div class=\"pdfEspacamentoLabels pdfEspacamentoCampos\" align=center ng-repeat=\"responsavel in laudoRelatorio.identificacao.responsaveisTecnicos\"><div class=pdfEspacamentoLabels><label class=negritoTexto>{{responsavel.nome}}</label></div><div class=pdfEspacamentoLabels><label>{{responsavel.regsitroProfissional == \'CREA\' ? \'CREA: \' : \'CAU: \'}}</label><label ng-bind=responsavel.crea></label></div></div><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=10 number=assinaturasNumber></contador-romano>- {{\'pdf-assinaturas\' | translate}}</u></h3></div><div class=m-b-20><table border=1 style=\"width: 100%\"><tr ng-repeat=\"homologador in laudoRelatorio.identificacao.responsaveisTecnicos\"><td class=alinhamentoTextoCentro><img src=assets/img/img-avaliacao/spu-net-logo-pdf.png></td><td>{{\'label-assinado-eletronicamente\' | translate}} <b><label ng-bind=homologador.nome></label></b>, <b><label ng-bind=\"\'label-responsavel-tecnico\' | translate\"></label></b>, {{\'label-crea-cau\' | translate}}: <b><label ng-bind=homologador.crea></label></b>,<label ng-show=homologador.isAssinado ng-bind=\"(\'label-em\' | translate) + \' \' + (homologador.dataAssinatura | date:\'dd/MM/yyyy\') + \', \' + (\'label-as\' | translate) + \' \' + (homologador.dataAssinatura | date:\'HH:mm\') + \'.\'\"></label></td></tr></table></div><div align=center class=\"pdfEspacamentoLabels negritoTexto pdfEspacamentoCampos\"><h3><u><contador-romano order=11 number=anexosNumber></contador-romano>- {{\'pdf-anexos\' | translate}}</u></h3></div><div ng-show=\"laudoRelatorio.anexo.anexos.length > 0\"><div ng-repeat=\"row in laudoRelatorio.anexo.anexos\" ng-if=\"row.selecionado && row.filhos.length > 0 &&\n                                                                                      ((laudoRelatorio.identificacao.metodologia == \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\' &&\n                                                                                      (row.nome != \'Arquivos de Orçamento\' && row.nome != \'Relatórios Estatísticos\' && row.nome != \'Orçamento/Fonte de Custos\')) ||\n                                                                                      ((laudoRelatorio.identificacao.metodologia == \'INFERENCIA_ESTATISTICA\' || laudoRelatorio.identificacao.metodoAvaliacao == \'COMPARATIVO_MERCADO\') &&\n                                                                                      (row.nome != \'Arquivos de Orçamento\' && row.nome != \'Orçamento/Fonte\' && row.nome != \'Orçamento/Fonte de Custos\' &&\n                                                                                      row.nome != \'Fonte BDI\' && row.nome != \'Fonte Depreciação\' && row.nome != \'Avaliação - Anexos\' && row.nome != \'Fonte Fator de Comercialização\')) ||\n                                                                                      ((laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria == \'ORCAMENTO\') &&\n                                                                                      (row.nome != \'Orçamento/Fonte\' && row.nome != \'Orçamento/Fonte de Custos\' && row.nome != \'Avaliação - Anexos\')) ||\n                                                                                      ((laudoRelatorio.identificacao.quantificacaoCustoBenfeitoria == \'CUB_PROJETO_SEMELHANTE\') &&\n                                                                                      (row.nome != \'Orçamento/Fonte\' && row.nome != \'Arquivos de Orçamento\' && row.nome != \'Avaliação - Anexos\'))) ||\n                                                                                      (row.nome==\'Memorial Descritivo do Imóvel\' && row.selecionado)\"><div layout=row layout-xs=column flex=100 class=\"negritoTexto m-t-20\"><h3><u>{{anexosNumber.value}}.<contador-romano order=(2+$index) parent=anexosNumber></contador-romano>- {{row.nome}}</u></h3></div><div ng-if=\"row.nome == \'Memorial Descritivo do Imóvel\'\" layout=row layout-xs=column flex=100 layout-align=\"center start\"><label>{{laudoRelatorio.terrenoImovel.terreno.memorialDescritivo}}</label></div><div ng-repeat=\"filho in row.filhos\" ng-if=filho.selecionado><div layout=row layout-xs=column flex=100 layout-align=\"center start\"><label>{{filho.nome}}</label></div><div layout=row layout-xs=column flex=100 layout-align=\"center start\"><span ng-if=filho.arquivo.id><img ng-src=\"{{gerarImagem(filho.arquivo.binario, filho.arquivo.contentType)}}\" class=pdfMarginTop flex=100 ng-if=\"filho.arquivo.contentType!=APPLICATION_PDF\"><iframe ng-src={{filho.sce}} class=pdfMarginTop flex=100 width=800 height=800 ng-if=\"filho.arquivo.contentType==APPLICATION_PDF\"></iframe></span><span ng-if=filho.imagemBenfeitoria.id><img ng-src=\"{{gerarImagem(filho.imagemBenfeitoria.imagem, filho.imagemBenfeitoria.contentType)}}\" class=pdfMarginTop flex=100> </span><span ng-if=filho.imagemImovel.id><img ng-src=\"{{gerarImagem(filho.imagemImovel.imagem, filho.imagemImovel.contentType)}}\" class=pdfMarginTop flex=100> </span><span ng-if=filho.imagemUnidadeAutonoma.id><img ng-src=\"{{gerarImagem(filho.imagemUnidadeAutonoma.arquivo, filho.imagemUnidadeAutonoma.contentType)}}\" class=pdfMarginTop flex=100> </span><span ng-if=filho.documentoEdificacao.arquivo.id><img ng-src=\"{{gerarImagem(filho.arquivo.arquivo, filho.arquivo.tipoArquivo)}}\" class=pdfMarginTop flex=100 ng-if=\"filho.arquivo.tipoArquivo!=APPLICATION_PDF\"><iframe ng-src={{filho.sce}} class=pdfMarginTop flex=100 height=700 ng-if=\"filho.arquivo.tipoArquivo==APPLICATION_PDF\"></iframe></span></div></div></div></div></div></div></div></md-content><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=redirecionarConsultarLaudoRelatorio();><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=assinarLaudoRelatorio()><label>{{\'button-assinar\' | translate}}</label></md-button></div></div></div></div>");
$templateCache.put("scripts-avaliacao/pages/pvg-incra-avaliacao/language/pt-BR.json","{ \"button-fechar\": \"Fechar\", \"placeholder-data\": \"Data\", \"title-avaliacao-terreno-referencia-incra\": \"Avaliação de Terreno Rural - referência INCRA\", \"title-valor\": \"Valor R$\", \"title-valor-terreno\": \"Valor do Terreno (R$)\", \"mensagem-data-inicial-maior-atual\": \"A data não pode ser posterior a data atual\", \"label-upload-arquivo\": \"Upload de Arquivo\", \"label-utilizar-documento-sei\": \"Utilizar Documento SEI\" }");
$templateCache.put("scripts-avaliacao/pages/pvg-incra-avaliacao/views/cadastrarAvaliacaoPvgIncra.html","<form name=formularioPVGIncra novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-avaliacao-terreno-referencia-incra\' | translate}}</h2></div></div><div class=upload-arquivos style=\"padding: 20px\" ng-if=\"pvgIncra.status === \'CANCELADO\'\"><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar style=\"border: none; padding: 0\">Dados do cancelamento</h3></div></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=50 flex-xs=50><label class=text-black>Data: <span ng-bind=\"pvgIncra.dataCancelamento | date: \'dd/MM/yyyy\' : \'-0300\'\"></span></label><input type=text ng-model=pvgIncra.dataRemocao disabled></md-input-container><md-input-container class=md-block layout=row flex=50 flex-xs=50><label class=text-black>Responsável</label><input type=text ng-model=pvgIncra.responsavelCancelamento disabled></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=100 flex-xs=100><label class=\"text-black layout-align-end-start\">Motivo:</label><input type=text ng-model=pvgIncra.motivoCancelamento disabled></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=100 flex-xs=100><label class=text-black>Justificativa:</label><textarea rows=4 cols=50 ng-model=pvgIncra.justificativaCancelamento disabled></textarea></md-input-container></div></div><div layout=row layout-xs=column><md-input class=\"md-block m-b-20\" flex=50 flex-xs flex-sm layout=column><span class=datepicker-label><span>{{\'title-unidade\' | translate}}:*</span></span><md-radio-group ng-required=true id=radioIncra class=\"tamanho-radio-button-avaliacao md-block\" ng-model=pvgIncra.tipoUnidade ng-change=formatarTipoUnidadeValor() layout=row ng-disabled=detalharPvgIncra><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTipoUnidades\" ng-value=k><div class=texto-preto>{{v}}</div></md-radio-button></div></md-radio-group></md-input></div><div layout=row layout-xs=column><div flex=50></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'title-valor-terreno\' | translate}}:</span></span></div></div><div layout=row layout-xs=column class=m-b-20><md-input-container class=md-block style=margin-bottom:0px flex=50 flex-sx=100 ng-if=\"pvgIncra.tipoUnidade == M2 || !pvgIncra.tipoUnidade\"><label>{{\'title-valor\' | translate}} {{formatterTipoUnidade}}:</label><input type=value ng-model=pvgIncra.valor ui-number-mask=2 ng-change=calcularValorTerreno() name=Valor required ng-disabled=\"detalharPvgIncra || !pvgIncra.tipoUnidade\"></md-input-container><md-input-container class=md-block style=margin-bottom:0px flex=50 flex-sx=100 ng-if=\"pvgIncra.tipoUnidade == HA\"><label>{{\'title-valor\' | translate}} {{formatterTipoUnidade}}:</label><input type=value ng-model=pvgIncra.valor ui-number-mask=2 ng-change=calcularValorTerreno() name=Valor required ng-disabled=\"detalharPvgIncra || !pvgIncra.tipoUnidade\"></md-input-container><md-container class=md-block flex=50 flex-sx=100><div ng-show=pvgIncra.valor><span>{{(pvgIncra.valorTerreno | currency).replace(\'R$\', \'\')}}</span></div></md-container></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'placeholder-data-inicial\' | translate}}:*</span></span></div></div><md-container flex=100 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div flex=100 layout=row layout-xs=row><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker flex=50 ng-model=pvgIncra.dataInicial ng-change=changeDataInicial() id=dataInicial md-max-date=hoje name=\"Data Inicial\" required md-placeholder=\"{{\'placeholder-data-inicial\' | translate}}*\" ng-disabled=detalharPvgIncra></md-datepicker></div></div></md-container><div layout=column ng-show=\"!detalharPvgIncra || (detalharPvgIncra && pvgIncra.tipoArquivo != null)\" flex><label class=sidenavTitulos>{{\'label-selecione\' | translate}}</label><md-radio-group class=m-t-10 flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=pvgIncra.tipoArquivo ng-disabled=detalharPvgIncra ng-change=limparDadosDocumentos()><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=10 ng-value=\"\'UPLOAD_ARQUIVO\'\"><div class=texto-preto>{{\'label-upload-arquivo\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=15 ng-value=\"\'DOCUMENTO_SEI\'\"><div class=texto-preto>{{\'label-utilizar-documento-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><documento-sei ng-if=\"pvgIncra.tipoArquivo == \'DOCUMENTO_SEI\'\" detalhar-laudo-avaliacao=detalharPvgIncra lista-documentos-laudo=pvgIncra.documentos uf=imovel.enderecamento.uf titulo=\"\'label-incluir-documentos\' | translate\"></documento-sei><documento-laudo-avaliacao-padrao ng-if=\"pvgIncra.tipoArquivo == \'UPLOAD_ARQUIVO\'\" lista-documentos-laudo=pvgIncra.documentos campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" titulo=\"\'label-incluir-documentos\' | translate\" detalhar-laudo-avaliacao=detalharPvgIncra></documento-laudo-avaliacao-padrao><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button ng-show=!detalharPvgIncra class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarDocumentoPVGIncra(formularioPVGIncra)><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/pvg-incra-avaliacao/views/cancelarAvaliacaoPvgIncra.html","<form name=formularioCancelarPvg novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{titulo}}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-data\' | translate}}:</label><input type=text ng-disabled=true ng-model=dataMostrar></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-nome-responsavel\' | translate}}:</label><input type=text ng-disabled=true ng-model=nomeResponsavel.responsavelCancelamento></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100><label>{{\'label-motivo\' | translate}}:</label><md-select id=motivoCancelamento ng-model=selecioneMotivo name=\"Motivo Cancelamento\" required><md-option ng-repeat=\"(k,v) in motivosCancelar\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div class=m-b-30 layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-offset=5><label>{{\'label-justificativa\' | translate}}</label><textarea rows=4 cols=50 ng-model=justificativa maxlength=500 required></textarea><span style=\"color: #cc0000; float: right\">{{0 + justificativa.length}} / 500</span></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=cancelar(formularioCancelarPvg);><label>{{\'button-confirmar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/pvg-municipio-avaliacao/language/pt-BR.json","{ \"button-fechar\": \"Fechar\", \"placeholder-data\": \"Data\", \"title-avaliacao-terreno-referencia-valor-venal-municipio\": \"Avaliação de Terreno - referência Valor Venal/Município\", \"title-valor\": \"Valor R$\", \"title-valor-terreno\": \"Valor do Terreno (R$)\", \"label-upload-arquivo\": \"Upload de Arquivo\", \"label-utilizar-documento-sei\": \"Utilizar Documento SEI\" }");
$templateCache.put("scripts-avaliacao/pages/pvg-municipio-avaliacao/views/cadastrarAvaliacaoPvgMunicipio.html","<form name=formularioPVGMunicipio novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-avaliacao-terreno-referencia-valor-venal-municipio\' | translate}}</h2></div></div><div class=upload-arquivos style=\"padding: 20px\" ng-if=\"pvgMunicipio.status === \'CANCELADO\'\"><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar style=\"border: none; padding: 0\">Dados do Cancelamento</h3></div></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=50 flex-xs=50><label class=text-black>Data: <span ng-bind=\"pvgMunicipio.dataCancelamento | date: \'dd/MM/yyyy\' : \'-0300\'\"></span></label><input type=text ng-model=pvgMunicipio.dataRemocao disabled></md-input-container><md-input-container class=md-block layout=row flex=50 flex-xs=50><label class=text-black>Responsável</label><input type=text ng-model=pvgMunicipio.responsavelCancelamento disabled></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=100 flex-xs=100><label class=\"text-black layout-align-end-start\">Motivo:</label><input type=text ng-model=pvgMunicipio.motivoCancelamento disabled></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=100 flex-xs=100><label class=text-black>Justificativa:</label><textarea rows=4 cols=50 ng-model=pvgMunicipio.justificativaCancelamento disabled></textarea></md-input-container></div></div><div layout=row layout-xs=column><div flex=50></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'title-valor-terreno\' | translate}}:</span></span></div></div><div layout=row layout-xs=column><md-input-container class=md-block style=margin-bottom:0px flex=50 flex-sx=100><label>{{\'title-valor\' | translate}}/m²:</label><input type=value ng-model=pvgMunicipio.valor ui-number-mask=2 ng-change=calcularValorTerreno() name=Valor required ng-disabled=detalharPvgMunicipio></md-input-container><md-container class=md-block flex=50 flex-sx=100><div ng-show=pvgMunicipio.valor><span>{{(pvgMunicipio.valorTerreno | currency).replace(\'R$\', \'\')}}</span></div></md-container></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'placeholder-data-inicial\' | translate}}:*</span></span></div></div><md-container flex=100 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div flex=100 layout=row layout-xs=row><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker flex=50 ng-model=pvgMunicipio.dataInicial ng-change=changeDataInicial() id=dataInicial md-max-date=hoje name=\"Data Inicial\" required md-placeholder=\"{{\'placeholder-data-inicial\' | translate}}*\" ng-disabled=detalharPvgMunicipio></md-datepicker></div></div></md-container><div layout=column ng-show=\"!detalharPvgMunicipio || (detalharPvgMunicipio && pvgMunicipio.tipoArquivo != null)\" flex><label class=sidenavTitulos>{{\'label-selecione\' | translate}}</label><md-radio-group class=m-t-10 flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=pvgMunicipio.tipoArquivo ng-disabled=detalharPvgMunicipio ng-change=limparDadosDocumentos()><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=10 ng-value=\"\'UPLOAD_ARQUIVO\'\"><div class=texto-preto>{{\'label-upload-arquivo\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=15 ng-value=\"\'DOCUMENTO_SEI\'\"><div class=texto-preto>{{\'label-utilizar-documento-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><documento-sei ng-if=\"pvgMunicipio.tipoArquivo == \'DOCUMENTO_SEI\'\" detalhar-laudo-avaliacao=detalharPvgMunicipio lista-documentos-laudo=pvgMunicipio.documentos titulo=\"\'label-incluir-documentos\' | translate\" uf=imovel.enderecamento.uf></documento-sei><documento-laudo-avaliacao-padrao ng-if=\"pvgMunicipio.tipoArquivo == \'UPLOAD_ARQUIVO\'\" lista-documentos-laudo=pvgMunicipio.documentos campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" titulo=\"\'label-incluir-documentos\' | translate\" detalhar-laudo-avaliacao=detalharPvgMunicipio></documento-laudo-avaliacao-padrao><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button ng-show=!detalharPvgMunicipio class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarDocumentoPVGMunicipio(formularioPVGMunicipio)><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/pvg-municipio-avaliacao/views/cancelarAvaliacaoPvgMunicipio.html","<form name=formularioCancelarPvg novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{titulo}}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-data\' | translate}}:</label><input type=text ng-disabled=true ng-model=dataMostrar></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-nome-responsavel\' | translate}}:</label><input type=text ng-disabled=true ng-model=nomeResponsavel.responsavelCancelamento></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100><label>{{\'label-motivo\' | translate}}:</label><md-select id=motivoCancelamento ng-model=selecioneMotivo name=\"Motivo Cancelamento\" required><md-option ng-repeat=\"(k,v) in motivosCancelar\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div class=m-b-30 layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-offset=5><label>{{\'label-justificativa\' | translate}}</label><textarea rows=4 cols=50 ng-model=justificativa maxlength=500 required></textarea><span style=\"color: #cc0000; float: right\">{{0 + justificativa.length}} / 500</span></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=cancelar(formularioCancelarPvg);><label>{{\'button-confirmar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/pvg-spu-avaliacao/language/pt-BR.json","{ \"button-fechar\": \"Fechar\", \"button-confirmar\": \"Confirmar\", \"placeholder-data\": \"Data\", \"mensagem-dados-desvinculados-sucesso\": \"Dados desvinculados com sucesso\", \"mensagem-cancelar-trechos-vinculados\": \"Essa ação irá cancelar os trechos ja vinculados ao imóvel. Deseja continuar?\", \"mensagem-remover-fatores-incluidos\":\"Essa alteração irá remover todos os fatores incluídos. Deseja continuar?\", \"mensagem-remover-fator\":\"Tem certeza que deseja remover este fator?\", \"title-desvincular-pgv-spu\": \"Desvincular PVG/SPU\", \"title-cancelar-pgv-spu\": \"Cancelar todas PVG/SPU\", \"title-avaliacao-terreno-pvg-spu\": \"Avaliação de Terreno - referência PVG/SPU\", \"title-valor\": \"Valor R$\", \"title-valor-terreno\": \"Valor do Terreno (R$)\", \"label-selecione\": \"Selecione\", \"label-upload-arquivo\": \"Upload de Arquivo\", \"label-utilizar-documento-sei\": \"Utilizar Documento SEI\" }");
$templateCache.put("scripts-avaliacao/pages/pvg-spu-avaliacao/views/cadastrarAvaliacaoPvgSpu.html","<form name=formularioPVGSpu novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-avaliacao-terreno-pvg-spu\' | translate}}</h2></div></div><div class=upload-arquivos style=\"padding: 20px\" ng-if=\"pvgSpu.statusAvaliacao === \'CANCELADO\'\"><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar style=\"border: none; padding: 0\">Dados da remoção do vínculo da PVG/SPU</h3></div></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=50 flex-xs=50><label class=text-black>Data: <span ng-bind=\"pvgSpu.dataCancelamento | date: \'dd/MM/yyyy\' : \'-0300\'\"></span></label><input type=text ng-model=pvgSpu.dataRemocao disabled></md-input-container><md-input-container class=md-block layout=row flex=50 flex-xs=50><label class=text-black>Responsável</label><input type=text ng-model=pvgSpu.responsavelCancelamento disabled></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=100 flex-xs=100><label class=\"text-black layout-align-end-start\">Motivo:</label><input type=text ng-model=pvgSpu.motivoCancelamento disabled></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=100 flex-xs=100><label class=text-black>Justificativa:</label><input type=text ng-model=pvgSpu.justificativaCancelamento disabled></md-input-container></div></div><br><div layout=row ng-show=\"showAvaliacoesTerrenos && !isEditarFator\"><md-input-container class=md-block flex=100 flex-xs=100 flex-offset=5><label>{{\'title-motivo-da-alteracao\' | translate}}:</label><md-select ng-model=pvgSpu.motivo ng-disabled=detalharPvgSpu><md-option ng-value=\"\">{{\'Selecione...\'}}</md-option><md-option ng-repeat=\"(k,v) in motivoAvaliacoes\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div layout=row ng-show=\"isCepGenerico  && !isEditarFator\" layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-offset=5><label>{{\'label-logradouro\' | translate}}:</label><md-select ng-model=logradouroPvgSelecionado ng-change=buscarTrechosPorLogradouro() ng-disabled=detalharPvgSpu><md-option ng-value=\"\">{{\'Selecione...\'}}</md-option><md-option ng-repeat=\"logradouro in logradourosSiapa\" ng-value=logradouro>{{logradouro.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=33 flex-xs=100 flex-offset=5><label>{{\'label-trecho\' | translate}}:</label><md-select ng-model=pvgSpu.trecho ng-change=trechoSelecionado() ng-disabled=\"detalharPvgSpu || isEditarFator\" required><md-option ng-value=\"\">{{\'Selecione...\'}}</md-option><md-option ng-repeat=\"trecho in listaTrechos\" ng-value=trecho>{{trecho.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block layout=row flex=33 flex-xs=100 ng-show=pvgSpu.trecho flex-offset=5><label class=text-black>{{\'label-valor-do\' | translate}} m²: <span ng-bind=\"(pvgSpu.valorAvaliacao | currency).replace(\'R$\', \'\')\"></span></label></md-input-container><md-input-container class=md-block layout=row flex=33 flex-xs=100 ng-show=pvgSpu.trecho><label class=text-black>{{\'label-ultima-atualizacao\' | translate}}: <span ng-bind=\"pvgSpu.ultimaAtualizacaoAvaliacao | date: \'dd/MM/yyyy\' : \'-0300\'\"></span></label></md-input-container></div><div layout=row layout-xs=column><md-checkbox class=md-primary ng-model=pvgSpu.adicionarFatores name=\"{{\'label-adicionar-fatores\' | translate}}\" ng-disabled=detalharPvgSpu ng-change=changeAdicionarFator()><label class=text-black>{{\'label-adicionar-fatores\' | translate}}</label></md-checkbox></div><div class=upload-arquivos style=\"padding: 20px\" ng-show=pvgSpu.adicionarFatores><div layout=row layout-xs=column><div flex><h2 class=subtitulo-pagina>{{\'title-fatores\' | translate}}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=50 flex-xs=50><label class=text-black>{{\'label-fator\' | translate}}:*</label><input type=text ng-model=fator.descricao maxlength=50 ng-disabled=detalharPvgSpu></md-input-container><md-input-container class=md-block layout=row flex=50 flex-xs=50><label class=text-black>{{\'label-valor\' | translate}}:*</label><input type=value ui-number-mask=3 ng-model=fator.valor ng-disabled=detalharPvgSpu></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block layout=row flex=100 flex-xs=100><label class=text-black>{{\'label-justificativa\' | translate}}:*</label><textarea type=text ng-model=fator.justificativa ng-disabled=detalharPvgSpu></textarea></md-input-container></div><div layout=column flex><label class=sidenavTitulos>{{\'label-selecione\' | translate}}</label><md-radio-group class=m-t-10 flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=fator.tipoArquivo ng-disabled=detalharPvgSpu ng-change=limparDadosArquivos()><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=10 ng-value=\"\'UPLOAD_ARQUIVO\'\"><div class=texto-preto>{{\'label-upload-arquivo\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=15 ng-value=\"\'DOCUMENTO_SEI\'\"><div class=texto-preto>{{\'label-utilizar-documento-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><documento-sei ng-if=\"fator.tipoArquivo == \'DOCUMENTO_SEI\'\" detalhar-laudo-avaliacao=detalharPvgSpu lista-documentos-laudo=fator.arquivos titulo=\"\'Arquivos\'\" uf=imovel.enderecamento.uf></documento-sei><documento-laudo-avaliacao-padrao ng-if=\"fator.tipoArquivo == \'UPLOAD_ARQUIVO\'\" detalhar-laudo-avaliacao=detalharPvgSpu lista-documentos-laudo=fator.arquivos tipo-validacao=\"\'CONTENT_TYPE_02\'\" titulo=\"\'Arquivos\'\" campo-data=\"\'true\'\"></documento-laudo-avaliacao-padrao><div layout=row layout-xs=column layout-align=end><div class=md-block flex-gt-xs flex=15 flex-xs=15 ng-if=!detalharPvgSpu><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ng-click=limparFator()><label>{{\'button-limpar\' | translate}}</label></md-button></div><div class=md-block flex-gt-xs flex=15 flex-xs=15 ng-show=!isEditar ng-if=!detalharPvgSpu><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ng-click=adicionarFator(fator)><label>{{\'button-adicionar\' | translate}}</label></md-button></div><div class=md-block flex-gt-xs flex=15 flex-xs=15 ng-show=isEditar ng-if=!detalharPvgSpu><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ng-click=editar(fator) ng-disabled=\"fator.arquivos.length >= 3\"><label>{{\'button-editar\' | translate}}</label></md-button></div></div><br><div layout=row layout-xs=column><div flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" md-column>{{\'label-fator\' | translate}}</th><th style=\"width: 40%\" md-column>{{\'label-justificativa\' | translate}}</th><th style=\"width: 20%\" md-column>{{\'title-arquivos\' | translate}}</th><th style=\"width: 10%\" md-column>{{\'label-valor\' | translate}}</th><th style=\"width: 10%\" md-column ng-show=!detalharPvgSpu>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"fator in pvgSpu.fatores\"><td md-cell><label ng-bind=fator.descricao>fator</label></td><td md-cell><label>{{fator.justificativa}}</label><a href=javascript:void(0); class=btn-ver-mais></a><div><script>$(\".btn-ver-mais\").unbind(\'click\');</script></div><div ng-if=\"pvgSpu.fatores.length - 1 == $index\"><script>$(\"md-table-container .btn-ver-mais\").on(\'click\', function () {\n                                            if ($(this).parent().find(\".descricao-tabela-pvg-spu\").length > 0) {\n                                                var content = $(this).parent().find(\".descricao-tabela-pvg-spu\");\n                                                content.removeClass(\"descricao-tabela-pvg-spu\");\n                                                $(this).html(\"ver menos...\");\n                                            } else {\n                                                var content = $(this).parent().find(\"label\");\n                                                if((content.height() / 18) > 2) {\n                                                    content.addClass(\"descricao-tabela-pvg-spu\");\n                                                    $(this).html(\"ver mais...\");\n                                                }\n                                            }\n                                        });\n\n                                        setTimeout(function() {\n                                            $(\"md-table-container tbody tr:nth-last-child(2) .btn-ver-mais\").click();\n                                        }, 100)</script></div></td><td md-cell><div ng-if=\"fator.arquivos.length > 0\"><label class=\"label-tabela arquivo-tabela\"><p ng-repeat=\"arquivo in fator.arquivos\"><a ng-if=\"fator.tipoArquivo == \'UPLOAD_ARQUIVO\'\" ng-href={{baixarArquivo(arquivo.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=arquivo.arquivo.nome></label></a><a ng-if=\"fator.tipoArquivo == \'DOCUMENTO_SEI\'\" ng-href={{baixarArquivo(arquivo.numeroDocumentoSei)}} target=_blank><label class=mouse-pointer ng-bind=arquivo.numeroDocumentoSei></label></a></p></label><a ng-if=\"fator.arquivos.length > 2\" href=javascript:void(0); class=btn-ver-mais-arquivo>ver mais...</a><div ng-if=\"fator.arquivos.length > 2\"><script>$(\".btn-ver-mais-arquivo\").unbind(\'click\');</script></div><div ng-if=\"pvgSpu.fatores.length - 1 == $index\"><script>$(\".btn-ver-mais-arquivo\").on(\'click\', function () {\n                                                if ($(this).parent().find(\".arquivo-tabela\").length > 0) {\n                                                    var content = $(this).parent().find(\".arquivo-tabela\");\n                                                    content.removeClass(\"arquivo-tabela\");\n                                                    $(this).html(\"ver menos...\");\n                                                } else {\n                                                    var content = $(this).parent().find(\"label.label-tabela\");\n                                                    content.addClass(\"arquivo-tabela\");\n                                                    $(this).html(\"ver mais...\");\n                                                }\n                                            })</script></div></div></td><td md-cell><label ng-bind=\"fator.valor | number:3\"></label></td><td md-cell width=50px ng-show=!detalharPvgSpu><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=removerFator($index); ng-show=!detalharPvgSpu><md-icon ng-bind=\"\'delete\'\"><md-tooltip>{{\'label-excluir-fator\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=editarFator($index,fator); ng-show=!detalharPvgSpu><md-icon ng-bind=\"\'edit\'\"><md-tooltip>{{\'label-editar-fator\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr><tr md-row ng-show=\"!pvgSpu.fatores || pvgSpu.fatores.length < 1\"><td md-cell colspan=6><label>Nenhum Fator adicionado</label></td></tr></tbody></table></md-table-container></div></div></div><div layout=row layout-xs=column ng-show=pvgSpu.adicionarFatores><span>Fator Corretivo Total: <b>{{pvgSpu.fatorCorretivoTotal | number:3}}</b> </span><span ng-show=mostrarMsgfator style=\"font-size: 12px; color: red; padding-top: 2px; padding-left: 5px\">Fator corrigido por estar fora do intervalo admissível pela norma (0,4 - 2,5)</span></div><div layout=row layout-xs=column><span>Valor Terreno: <b ng-bind=\"pvgSpu.valorTerreno | currency\"></b></span></div><div style=\"margin-top: 40px\" layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button ng-show=!detalharPvgSpu class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarAvaliacaoPvgSpu(formularioPVGSpu)><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/pvg-spu-avaliacao/views/desvincularAvaliacaoPvgSpu.html","<form name=formularioDesvincularPvg novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'title-desvincular-pgv-spu\' | translate}}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-nome-responsavel\' | translate}}:</label><input type=text ng-disabled=true ng-model=pvgSpu.responsavelCancelamento></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-data\' | translate}}:</label><input type=text ng-disabled=true ng-model=dataMostrar></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50><label>{{\'label-trecho\' | translate}}:</label><md-select ng-model=pvgSpu.trecho required><md-option ng-repeat=\"item in trechos\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50><label>{{\'label-motivo\' | translate}}:</label><md-select id=motivoCancelamento ng-model=pvgSpu.motivoCancelamento name=\"Motivo Cancelamento\" required><md-option ng-repeat=\"(k,v) in motivosCancelar\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div class=m-b-30 layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-offset=5><label>{{\'label-justificativa\' | translate}}</label><textarea rows=4 cols=50 ng-model=pvgSpu.justificativaCancelamento maxlength=500 required></textarea><span style=\"color: #cc0000; float: right\">{{0 + pvgSpu.justificativaCancelamento.length}} / 500</span></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=desvincular(formularioDesvincularPvg);><label>{{\'button-confirmar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/revalidacao-laudo-avaliacao/language/pt-BR.json","{ \"button-recusar\": \"Recusar\", \"button-fechar\": \"Fechar\", \"button-confirmar-selecao\": \"Confirmar Seleção\", \"button-adicionar\": \"Adicionar\", \"button-assinar\": \"Assinar\", \"button-cancelar\": \"Cancelar\", \"button-ver-detalhamento-do-laduo\": \"Ver detalhamento do Laudo\", \"button-ver-detalhamento-do-relatorio\": \"Ver detalhamento do Relatório\", \"button-pre-visualizar\": \"Pré-Visualizar Documento de Revalidação\", \"button-salvar-rascunho\": \"Salvar como Rascunho\", \"button-gravar-assinar-revalidacao\": \"Assinar Revalidação e Gravar\", \"label-cpf-homologador\": \"CPF do Homologador\", \"label-cpf-homologador-se-laudo-contratado\": \"CPF do Homologador (se laudo contratado)\", \"label-cpf-responsavel-tecnico\": \"CPF do Responsável Técnico\", \"label-data-de-assinatura\": \"Data da assinatura\", \"label-moeda\": \"Moeda\", \"label-nome-homologador\": \"Nome do Homologador\", \"label-nome-responsavel-tecnico\": \"Nome do Responsável Tecnico\", \"label-numero-crea-responsavel\": \"Nº do CREA/CAU do Responsável Técnico\", \"label-tipo-avaliacao\": \"Tipo de avaliação\", \"label-valor-imovel\": \"Valor do Imóvel\", \"label-utilizar-laudo-preenchido\": \"Utilizar Laudo/Relatório preenchido no sistema?\", \"label-sim\": \"Sim\", \"label-nao\": \"Não\", \"label-selecionar-laudo\": \"Selecionar Laudo/Relatório\", \"label-responsavel-tecnico\": \"Responsável Técnico\", \"label-valor-terreno-benfeitoria\": \"Valores de Terreno e Benfeitoria\", \"label-laudo-relatorio-elaborado\": \"Laudo/Relatório Elaborado\", \"label-adicionar\": \"Adicionar\", \"label-assinatura-nota-tecnica\": \"Assinatura da Nota Técnica\", \"label-acoes\": \"Ações\", \"label-editar\": \"Editar\", \"label-excluir\": \"Excluir\", \"label-nota-tecnica-homologacao\": \"Nota Técnica de Homologação\", \"label-upload-arquivo\": \"Upload de Arquivo (PDF)\", \"label-preencher-sistema\": \"Preencher no Sistema\", \"label-preencher-alterar-nota\": \"Preencher/Alterar Nota Técnica\", \"label-situacao\": \"Situação\", \"label-laudo-avaliacao-relatorio\": \"Laudo de Avaliação/Relatório de Referência\", \"label-ministerio-planejamento\": \"MINISTÉRIO DO PLANEJAMENTO, DESENVOLVIMENTO E GESTÃO\", \"label-secretaria-patrimonio-da-uniao\": \"Secretaria do Patrimônio da União\", \"label-nota-tecnica-homologacao-laudo-avaliacao\": \"Nota Técnica de Homologação do Laudo/Relatório de Avaliação\", \"label-sumario-executivo\": \"Sumário Executivo\", \"label-id\": \"ID\", \"label-tipo\": \"Tipo\", \"label-descricao\": \"Descrição\", \"label-area-construida\": \"Área Construída\", \"label-vida-util\": \"Vida Útil (Ano)\", \"label-cub\": \"CUB\", \"label-idade-aparente\": \"Idade Aparente (Ano)\", \"label-valor-estimado\": \"Valor Estimado\", \"label-valor-estimado-total\": \"VALOR ESTIMADO TOTAL\", \"label-fonte-valor-terreno\": \"Fonte do valor do terreno\", \"label-pvg-spu\": \"PVG/SPU\", \"label-valor-venal\": \"Valor Venal/Município\", \"label-referencia-incra\": \"Referência INCRA\", \"label-outra-fonte\": \"Outra Fonte\", \"label-valor-terreno-rs\": \"Valor do Terreno (R$)\", \"label-valor-m2\": \"Valor do m² (R$)\", \"label-periodo-avaliacao\": \"Período da Avaliação\", \"label-valor-terreno\": \"Valor do Terreno\", \"label-fonte\": \"Fonte\", \"label-numero\": \"Número\", \"label-metodo\": \"Método\", \"label-data-assinatura\": \"Data da Assinatura\", \"label-valor\": \"Valor\", \"label-responsavel\": \"Responsável\", \"label-confirmar-selecao\": \"Confirmar Seleção\", \"label-verificacao-itens-obrigatorios-intrucao-normativa\": \"Verificação de Itens Obritatórios Segundo a Instrução Normativa\", \"label-aspectos\": \"Aspectos\", \"label-verificacao\": \"Verificação\", \"label-observacao-justificativa\": \"Observação/Justificativa\", \"label-atende\": \"Atende\", \"label-nao-atende\": \"Não Atende\", \"label-aspectos-I\": \"I - a identificação da pessoa física ou jurídica e/ou seu representante legal que solicitou o serviço;\", \"label-aspectos-II\": \"II - o objetivo e finalidade da avaliação;\", \"label-aspectos-III\": \"III - a identificação e caracterização do bem avaliando;\", \"label-aspectos-IV\": \"IV - a verificação da metodologia utilizada e sua justificativa;\", \"label-aspectos-V\": \"V - a especificação da valiação quanto à indicação dos graus de fundamentação e precisão atingidos, confirmando-os quando couber, coma exibição das tabelas previstas da NBR 14.653, devidamente pontuadas, em consonância coma metodologia adotada no laudo\", \"label-aspectos-VI\": \"VI - a qualificação legal completa e assinatura do(s) profissional(is) responsável(is) pela avaliação;\", \"label-aspectos-VII\": \"VII - a conferência da validade da avaliação em observância às orientações vigentes da SPU, dentre elas:\", \"label-aspectos-VIII\": \"VIII - a verificação quanto ao recolhimento da ART, quando for o caso;\", \"label-aspectos-IX\": \"IX - a verificação quanto ao tratamento dos dados e identificalção do resultado, explicitação do campo de arbítrio e intervalos de confiança, se for o caso, e justificativas para o resultado adotado;\", \"label-aspectos-X\": \"X - a confirmação do valor determinado na equação apresentada.\", \"label-aspectos-VII-a\": \"a) apresentação do gráfico \\\"preços observados versus valores estimados pelo modelo\\\", conforme item 8.2.1.4.1 da NBR 14.653-2;\", \"label-aspectos-VII-b\": \"b) quando a utilização do Tratamento de Dados por Fatores, deverá apresentar a sua validação (publicações de entidades técnicas reconhecidas ou deduzidos ou referendados pelo próprio avaliador), conforme item 8.2.1.4.1 da NBR 14.653-2;\", \"label-aspectos-VII-c\": \"c) em caso de existência de \\\"outliers\\\", deverá apresentar análise de aceitação;\", \"label-aspectos-VII-d\": \"d) se o coeficiente de correlação for inferior a 0.70 para os laudos de avaliação elaborados com tratamento científico de dados de mercado, deverá ser justificado;\", \"label-aspectos-VII-e\": \"e) caso a correlação entre as variáveis independentes seja superior a 0,84, deverá apresentar justificativas;\", \"label-conclusao\": \"Conclusão\", \"label-assinaturas\": \"Assinaturas\", \"label-homologador\": \"Homologador\", \"label-revalidacao-laudo\": \"Revalidação do Laudo\", \"label-revalidacao-relatorio\": \"Revalidação do Relatório\", \"label-revalidacao-laudo-avaliacao\": \"Revalidação do Laudo de Avaliação\", \"label-analise\": \"Análise\", \"label-assinado-eletronicamente\": \"Documento assinado eletronicamente por\", \"label-em\": \"em\", \"label-termo-responsabilidade\": \"Termo de Responsabilidade\", \"label-texto-info-verdadeira\": \"Certifico que as informações prestadas são verdadeiras e que o arquivo do Laudo/Relatório de Avaliação está correto.\", \"label-orgao\": \"Órgão\", \"label-unidade\": \"Unidade\", \"label-subunidade\": \"Subunidade\", \"label-utilizar-documento-sei\": \"Utilizar Documento SEI\", \"label-enviar-documento-para-sei\": \"Enviar documento para o SEI\", \"label-criar-novo-processo-sei\": \"Criar novo processo no SEI\", \"label-utilizar-processo-existente-sei\": \"Utilizar processo existente no SEI\", \"label-numero-processo-sei\": \"Número do Processo SEI\", \"label-n-processo\": \"N° do Processo\", \"label-validade-atual\": \"Validade atual\", \"label-validade-estendida\": \"Validade estendida\", \"placeholder-data-assinatura\": \"Data da assinatura\", \"title-inserir-dados-laudo-relatorio-avaliacao\": \"Inserir Dados do Laudo/Relatório de Avaliação\", \"title-anexos\": \"Anexos\", \"title-extensao-da-validade-laudo\": \"Extensão da Validade do Laudo de Avaliação\", \"subtitle-extensao-da-validade-laudo\": \"(a partir da data Final de Validade)\", \"12-meses\": \"12 Meses\", \"24-meses\": \"24 Meses\", \"mensagem-termo-responsabilidade-laudo-avaliacao\": \"certifico que as informações prestadas são verdadeiras e que o arquivo da Nota Técnica de Revalidação do Laudo de Avaliação está correto. O valor atribuído ao imóvel é de inteira responsabilidade do avaliador responsável pela avaliação.\", \"mensagem-valor-imovel-diferente-valor-terreno\": \"O valor da área construída e do terreno não corresponde ao valor do imóvel.\", \"mensagem-data-assinatura-posterior-data-atual\": \"A data da assinatura não pode ser posterior a data atual.\", \"mensagem-necessario-informar-responsavel-tecnico\": \"Necessário informar responsável técnico.\", \"mensagem-necessario-informar-crea-responsavel\": \"Necessario informar CREA do responsável técnico.\", \"mensagem-recusar-revalidacao\": \"Tem certeza que deseja recusar a revalidação do Laudo?\", \"mensagem-responsaveis-revalidacao-removidos-nao-usuarios-sistema\": \"Os responsáveis pela revalidação abaixo serão removidos pois não são usuários do sistema:\", \"mensagem-deseja-alterar-tipo-revalidacao\": \"Deseja alterar o tipo da Revalidação do Laudo/Relatório?\", \"mensagem-adicionar-laudo-avaliacao\":\"Necessário adicionar um laudo de avaliação.\" }");
$templateCache.put("scripts-avaliacao/pages/revalidacao-laudo-avaliacao/views/assinarRevalidarLaudoAvaliacao.html","<div class=marginModal><div layout=row flex=100><div class=logo-assinar-termo><img width=150px src=assets/img/img-avaliacao/logo_pdf_laudo_avaliacao.png></div><div layout=column class=positionRelative flex=100 style=\"min-height: 160px\"><div><h3 class=alinhamentoTela>{{revalidacaoLaudoAvaliacao.orgao}}</h3><h3 class=alinhamentoTela>{{revalidacaoLaudoAvaliacao.unidade}}</h3><h3 class=alinhamentoTela>{{revalidacaoLaudoAvaliacao.subunidade}}</h3><h2 class=\"negritoTexto alinhamentoTela\">{{\'label-revalidacao-laudo-avaliacao\' | translate}}{{\' Nº \' + laudoAvaliacao.numeroLaudo}}</h2></div></div></div><div layout=column class=m-t-20><div><span>{{\'label-sumario-executivo\' | translate}}:</span><p class=\"paragraph-padding margin-0\" style=\"text-align: justify\"><label ng-bind=revalidacaoLaudoAvaliacao.sumarioExecutivo></label></p></div><br><div><span>{{\'label-analise-laudo\' | translate}}:</span><p class=\"paragraph-padding margin-0\" style=\"text-align: justify\"><label ng-bind=revalidacaoLaudoAvaliacao.analise></label></p></div></div><br><div><span>{{\'label-conclusao\' | translate}}:</span><p class=\"paragraph-padding margin-0\" style=\"text-align: justify\"><label ng-bind=revalidacaoLaudoAvaliacao.consideracoesFinais></label></p></div><br><br><div class=alinhamentoTela ng-repeat=\"homologador in revalidacaoLaudoAvaliacao.responsaveis\"><span><b>{{homologador.nome}}</b></span><br><span>{{(homologador.registroProfissional == \'CREA\' ? \'CREA: \' : \'CAU: \') + homologador.crea}}</span><br><br></div><br><b>{{\'label-assinaturas\' | translate}}</b><table class=simple-table width=100%><tr ng-repeat=\"homologador in revalidacaoLaudoAvaliacao.responsaveis\"><td class=alinhamentoTextoCentro><img src=assets/img/img-avaliacao/spu-net-logo-pdf.png></td><td>{{\'label-assinado-eletronicamente\' | translate}} <b><label ng-bind=homologador.nome></label></b>, <b><label ng-bind=\"\'label-responsavel-tecnico\' | translate\"></label></b>, {{\'label-crea-cau\' | translate}}: <b><label ng-bind=homologador.crea></label></b>,<label ng-show=homologador.isAssinado ng-bind=\"(\'label-em\' | translate) + \' \' + (homologador.dataAssinatura | date:\'dd/MM/yyyy\') + \', \' + (\'label-as\' | translate) + \' \' + (homologador.dataAssinatura | date:\'HH:mm\') + \'.\'\"></label></td></tr></table><br><b ng-if=\"revalidacaoLaudoAvaliacao.anexos.length > 0\">{{\'title-anexos\' | translate}}</b><br><div layout=column ng-if=\"revalidacaoLaudoAvaliacao.anexos.length > 0\"><div layout=column ng-repeat=\"anexo in revalidacaoLaudoAvaliacao.anexos\"><br><b>{{\'Anexo \' + $index+1 + \" - \" + anexo.nome}}</b><div layout=row flex=100><img ng-src=\"{{gerarImagem(anexo.arquivo.binario, anexo.arquivo.contentType)}}\" class=pdfMarginTop ng-if=\"anexo.arquivo.contentType!=\'application/pdf\'\"><iframe ng-src={{anexo.sce}} flex=100 height=500 ng-if=\"anexo.arquivo.contentType==\'application/pdf\'\"></iframe><br></div></div></div><div layout=row layout-xs=column class=buttons-action style=\"margin-top: 40px\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarDetalharImovelAvaliacao()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=recusarRevalidarAssinaturaLaudoAvaliacao()><label>{{\'button-recusar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=assinarRevalidarLaudoAvaliacao()><label>{{\'button-assinar\' | translate}}</label></md-button></div></div></div></div>");
$templateCache.put("scripts-avaliacao/pages/revalidacao-laudo-avaliacao/views/revalidarLaudoRelatorioAvaliacao.html","<form name=formularioRevalidarLaudo novalidate><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{ laudoAvaliacao.tipoAvaliacao === \'LAUDO_RELATORIO_VALOR_REFERENCIA\' ? (\'label-revalidacao-relatorio\' | translate) : (\'label-revalidacao-laudo\' | translate) }}</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100><label>{{\'label-unidade\' | translate}}:</label><md-select id=unidade ng-model=revalidacaoLaudoAvaliacao.unidade name=\"{{\'label-unidade\' | translate}}\" ng-required=true ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"item in listaUnidades\" ng-value=item>{{item}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-subunidade\' | translate}}</label><input type=text maxlength=150 ng-model=revalidacaoLaudoAvaliacao.subunidade></md-input-container></div><responsavel-tecnico lista-responsaveis-tecnicos=revalidacaoLaudoAvaliacao.responsaveis incluir-usuario-logado=false assinatura-nota-tecnica=false detalhar-laudo-relatorio=false></responsavel-tecnico><md-input class=md-block flex=33 flex-xs=100 flex-sm=100 layout=column><label class=sidenavTitulos>{{\'label-revalidacao-laudo-avaliacao\' | translate}}*:</label><md-radio-group flex required ng-model=revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorioTemp ng-change=limparTipoRevalidacao() class=\"tamanho-radio-button-avaliacao md-block\"><div layout=row><md-radio-button ng-repeat=\"(k,v) in listaTipoNotaTecnicaHomologacao\" flex=30 flex-xs=100 flex-sm=100 flex-md=100 ng-value=k class=\"tamanho-radio-button-avaliacao md-primary\"><div class=texto-preto>{{v}}</div></md-radio-button></div></md-radio-group></md-input><div ng-if=\"revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio == UPLOAD_ARQUIVO\"><documento-laudo-avaliacao-padrao lista-documentos-laudo=revalidacaoLaudoAvaliacao.arquivosLaudoRevalidacao tipo-validacao=\"\'CONTENT_TYPE_01\'\" detalhar-laudo-avaliacao=false></documento-laudo-avaliacao-padrao></div><div ng-if=\"revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio == UPLOAD_ARQUIVO\"><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.isEnviarDocumento ng-change=changeDocumentoSei() ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-enviar-documento-para-sei\' | translate}}</label></md-checkbox></div><div ng-if=revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.isEnviarDocumento layout=column flex><md-radio-group flex class=\"m-l-30 tamanho-radio-button-avaliacao md-block\" ng-model=revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.tipoEnviarDocumento ng-disabled=\"detalharLaudoRelatorio || !revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.isEnviarDocumento\" ng-change=changeNumeroSei() ng-required=revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.isEnviarDocumento><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'CRIAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-criar-novo-processo-sei\' | translate}}</div></md-radio-button></div><div class=\"m-b-10 m-l-60\" layout=row ng-if=\"detalharLaudoRelatorio && revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.tipoEnviarDocumento == \'CRIAR_PROCESSO_SEI\' && revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei\"><div class=texto-preto>{{\'label-n-processo\' | translate}}{{\': \' + revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei | processoSei}}</div></div><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'UTILIZAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-utilizar-processo-existente-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><md-input-container ng-if=\"revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" class=\"md-block m-l-60\" flex=30 flex-xs=100 flex-sm=100><label>{{\'label-numero-processo-sei\' | translate}}</label><input type=text ui-mask=99999.999999/9999-99 ng-required=\"revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" ng-model=revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei ng-disabled=detalharLaudoRelatorio ng-blur=validarLaudoAvaliacaoNumeroSeiProcessoSei()></md-input-container></div><documento-sei ng-if=\"revalidacaoLaudoAvaliacao.revalidacaoLaudoRelatorio == DOCUMENTO_SEI\" detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=revalidacaoLaudoAvaliacao.arquivosLaudoRevalidacao uf=imovel.enderecamento.uf></documento-sei><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-anexos\' | translate}}</h3></div><div layout=column flex><label class=sidenavTitulos>{{\'label-selecione\' | translate}}</label><md-radio-group class=m-t-10 flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=revalidacaoLaudoAvaliacao.tipoArquivo ng-disabled=detalharLaudoRelatorio ng-change=limparAnexos()><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=10 ng-value=UPLOAD_ARQUIVO><div class=texto-preto>{{\'label-upload-arquivo\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=15 ng-value=DOCUMENTO_SEI><div class=texto-preto>{{\'label-utilizar-documento-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><documento-sei ng-if=\"revalidacaoLaudoAvaliacao.tipoArquivo == DOCUMENTO_SEI\" detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=revalidacaoLaudoAvaliacao.anexos uf=imovel.enderecamento.uf></documento-sei><documento-laudo-avaliacao-padrao ng-if=\"revalidacaoLaudoAvaliacao.tipoArquivo == UPLOAD_ARQUIVO\" lista-documentos-laudo=revalidacaoLaudoAvaliacao.anexos campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_04\'\" detalhar-laudo-avaliacao=false></documento-laudo-avaliacao-padrao><div layout=column layout-xs=column><h3 class=\"subtitulo-pagina m-b-0\">{{\'title-extensao-da-validade-laudo\' | translate}}</h3><p class=\"subtitulo-menor-pagina m-t-0\">{{\'subtitle-extensao-da-validade-laudo\' | translate}}</p></div><div layout=row flex><div layout=column flex=20><label class=sidenavTitulos>{{\'label-selecione\' | translate}}*:</label><md-radio-group required class=m-t-10 flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=revalidacaoLaudoAvaliacao.mesesRevalidacao ng-disabled=detalharLaudoRelatorio ng-require=true ng-change=calcularDatasEstendidas()><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" ng-value=DOZE_MESES><div class=texto-preto>{{\'12-meses\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary m-l-20\" ng-value=VINTE_E_QUATRO_MESES ng-if=\"laudoAvaliacao && laudoAvaliacao.tipoRevalidacao==VALIDAR_12OU24_MESES\"><div class=texto-preto>{{\'24-meses\' | translate}}</div></md-radio-button></md-radio-group></div><div layout=column><div layout=row class=\"m-t-5 m-b-10\"><div class=negritoTexto>{{\'label-validade-atual\' | translate}}:</div><div class=m-l-10>{{laudoAvaliacao.inicioPeriodo | date: \'dd/MM/yyyy\' : \'-0300\'}} a {{laudoAvaliacao.finalPeriodo | date: \'dd/MM/yyyy\' : \'-0300\'}}</div></div><div layout=row class=p-10><div class=negritoTexto>{{\'label-validade-estendida\' | translate}}:</div><div class=m-l-10 ng-if=finalEstendida>{{inicioEstendida | date: \'dd/MM/yyyy\' : \'-0300\'}} a {{finalEstendida | date: \'dd/MM/yyyy\' : \'-0300\'}}</div></div></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div class=m-b-20 layout=row flex=100><div flex=100 layout=column><md-checkbox class=md-primary ng-disabled=detalharLaudoRelatorio ng-model=revalidacaoLaudoAvaliacao.termoResponsabilidade.isAssinado name=\"{{\'label-termo-responsabilidade\' | translate}}\" ng-required=true><label class=text-black>{{\'Eu, \' + usuarioLogado.nome + \', CPF: \' + usuarioLogado.cpf + \', \'}}{{\'mensagem-termo-responsabilidade-laudo-avaliacao\' | translate}}</label></md-checkbox></div></div><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarParaDetalharImovel()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=redirecionarDetalharLaudoAvaliacao() style=\"margin: 0 0 0 15px\"><label>{{ laudoAvaliacao.tipoAvaliacao === \'LAUDO_RELATORIO_VALOR_REFERENCIA\' ? (\'button-ver-detalhamento-do-relatorio\' | translate) : (\'button-ver-detalhamento-do-laduo\' | translate) }}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=salvarComoRascunhoRevalidacao()><label>{{\'button-salvar-rascunho\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=revalidarLaudoAvaliacao()><label>{{\'button-gravar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/revalidacao-laudo-avaliacao/views/visualizarRevalidarLaudoAvaliacao.html","<div class=marginModal><div layout=row flex=100><div class=logo-assinar-termo><img width=150px src=assets/img/img-avaliacao/logo_pdf_laudo_avaliacao.png></div><div layout=column class=positionRelative flex=100 style=\"min-height: 160px\"><div><h3 class=alinhamentoTela>{{revalidacaoLaudoAvaliacao.orgao}}</h3><h3 class=alinhamentoTela>{{revalidacaoLaudoAvaliacao.unidade}}</h3><h3 class=alinhamentoTela>{{revalidacaoLaudoAvaliacao.subunidade}}</h3><h2 class=\"negritoTexto alinhamentoTela paddingTop10\">{{\'label-revalidacao-laudo-avaliacao\' | translate}}{{\' Nº \' + laudoAvaliacao.numeroLaudo}}</h2></div></div></div><div layout=column class=m-t-20><div><span><b>{{\'label-sumario-executivo\' | translate}}:</b></span><p class=\"paragraph-padding margin-0\" style=\"text-align: justify\"><label ng-bind=revalidacaoLaudoAvaliacao.sumarioExecutivo></label></p></div><br><div><span><b>{{\'label-analise\' | translate}}:</b></span><p class=\"paragraph-padding margin-0\" style=\"text-align: justify\"><label ng-bind=revalidacaoLaudoAvaliacao.analise></label></p></div></div><br><div><span><b>{{\'label-conclusao\' | translate}}:</b></span><p class=\"paragraph-padding margin-0\" style=\"text-align: justify\"><label ng-bind=revalidacaoLaudoAvaliacao.consideracoesFinais></label></p></div><br><br><div class=alinhamentoTela ng-repeat=\"homologador in revalidacaoLaudoAvaliacao.responsaveis\"><span><b>{{homologador.nome}}</b></span><br><span>{{(homologador.registroProfissional == \'CREA\' ? \'CREA: \' : \'CAU: \') + homologador.crea}}</span><br><br></div><br><b>{{\'label-assinaturas\' | translate}}</b><table class=simple-table width=100%><tr ng-repeat=\"homologador in revalidacaoLaudoAvaliacao.responsaveis\"><td class=alinhamentoTextoCentro><img src=assets/img/img-avaliacao/spu-net-logo-pdf.png></td><td>{{\'label-assinado-eletronicamente\' | translate}} <b><label ng-bind=homologador.nome></label></b>, <b><label ng-bind=\"\'label-responsavel-tecnico\' | translate\"></label></b>, {{\'label-crea-cau\' | translate}}: <b><label ng-bind=homologador.crea></label></b>,<label ng-show=homologador.isAssinado ng-bind=\"(\'label-em\' | translate) + \' \' + (homologador.dataAssinatura | date:\'dd/MM/yyyy\') + \', \' + (\'label-as\' | translate) + \' \' + (homologador.dataAssinatura | date:\'HH:mm\') + \'.\'\"></label></td></tr></table><div ng-if=\"revalidacaoLaudoAvaliacao.anexos.length > 0 && revalidacaoLaudoAvaliacao.tipoArquivo == \'UPLOAD_ARQUIVO\'\"><br><b>{{\'title-anexos\' | translate}}</b><br><div layout=column><div layout=column ng-repeat=\"anexo in revalidacaoLaudoAvaliacao.anexos\"><br><b>{{\'Anexo \' + $index+1 + \" - \" + anexo.nome}}</b><div layout=row flex=100><img ng-src=\"{{gerarImagem(anexo.arquivo.binario, anexo.arquivo.contentType)}}\" class=pdfMarginTop ng-if=\"anexo.arquivo.contentType!=\'application/pdf\'\"><iframe ng-src={{anexo.sce}} flex=100 height=500 ng-if=\"anexo.arquivo.contentType==\'application/pdf\'\"></iframe><br></div></div></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button ng-if=!detalharLaudoRelatorio class=\"md-primary md-raised\" type=button ng-click=voltarRevalidarLaudo()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button ng-if=detalharLaudoRelatorio class=\"md-primary md-raised\" type=button ng-click=voltarDetalharLaudo()><label>{{\'button-fechar\' | translate}}</label></md-button></div></div></div></div>");
$templateCache.put("scripts-avaliacao/pages/nota-tecnica/language/pt-BR.json","{ \"label-verificacao-itens-obrigatorios-intrucao-normativa\": \"Verificação de Itens Obritatórios Segundo a Instrução Normativa\", \"label-aspectos\": \"Aspectos\", \"label-verificacao\": \"Verificação\", \"label-observacao-justificativa\": \"Observação/Justificativa\", \"label-atende\": \"Atende\", \"label-nao-atende\": \"Não Atende\", \"label-aspectos-I\": \"I - a identificação da pessoa física ou jurídica e/ou seu representante legal que solicitou o serviço;\", \"label-aspectos-II\": \"II - o objetivo e finalidade da avaliação;\", \"label-aspectos-III\": \"III - a identificação e caracterização do bem avaliando;\", \"label-aspectos-IV\": \"IV - a verificação da metodologia utilizada e sua justificativa;\", \"label-aspectos-V\": \"V - a especificação da avaliação quanto à indicação dos graus de fundamentação e precisão atingidos, confirmando-os quando couber, com exibição das tabelas previstas da NBR 14.653, devidamente pontuadas, em consonância com metodologia adotada no laudo\", \"label-aspectos-VI\": \"VI - a qualificação legal completa e assinatura do(s) profissional(is) responsável(is) pela avaliação;\", \"label-aspectos-VII\": \"VII - a conferência da validade da avaliação em observância às orientações vigentes da SPU, dentre elas:\", \"label-aspectos-VIII\": \"VIII - a verificação quanto ao recolhimento da ART ou RRT, quando for o caso;\", \"label-aspectos-IX\": \"IX - a verificação quanto ao tratamento dos dados e identificação do resultado, explicitação do campo de arbítrio e intervalos de confiança, se for o caso, e justificativas para o resultado adotado;\", \"label-aspectos-X\": \"X - a confirmação do valor determinado na equação apresentada.\", \"label-aspectos-VII-a\": \"a) apresentação do gráfico \\\"preços observados versus valores estimados pelo modelo\\\", conforme item 8.2.1.4.2 da NBR 14.653-2;\", \"label-aspectos-VII-b\": \"b) quando da utilização do Tratamento de Dados por Fatores, deverá apresentar a sua validação (publicações de entidades técnicas reconhecidas ou deduzidos ou referendados pelo próprio avaliador), conforme item 8.2.1.4.1 da NBR 14.653-2;\", \"label-aspectos-VII-c\": \"c) em caso de existência de \\\"outliers\\\", deverá apresentar análise de aceitação;\", \"label-aspectos-VII-d\": \"d) se o coeficiente de correlação for inferior a 0.70 para os laudos de avaliação elaborados com tratamento científico de dados de mercado, deverá ser justificado;\", \"label-aspectos-VII-e\": \"e) caso a correlação entre as variáveis independentes seja superior a 0,84, deverá apresentar justificativas;\", \"label-consideracoes-finais\": \"Considerações Finais\", \"label-conclusao\": \"Conclusão\", \"label-assinaturas\": \"Assinaturas\", \"label-homologador\": \"Homologador\", \"label-documento-assinado-em\":\"Documento assinado eletônicamente em\", \"button-recusar\": \"Recusar\", \"button-fechar\": \"Fechar\", \"button-confirmar-selecao\": \"Confirmar Seleção\", \"label-responsabilidade-tecnica\": \"Responsabilidade Técnica\", \"label-responsabilidade-tecnica-valor\": \"Independentemente da homologação, o valor atribuído é de inteira responsabilidade do avaliador responsável pela avaliação\", \"label-parecer-tecnico\": \"Parecer Técnico\", \"label-radio-homologacao\": \"Sugiro pela homologação do laudo de avaliação\", \"label-radio-nao-homologacao\": \"Sugiro pela não homologação do laudo de avaliação\", \"label-revalidacao-laudo-avaliacao\": \"Revalidação do Laudo de Avaliação\", \"label-analise-mercado\": \"Análise de Mercado\", \"button-assinar\": \"Assinar\", \"label-assinado-eletronicamente\": \"Documento assinado eletronicamente por\", \"label-chefia\": \"Chefia\", \"label-sumario-executivo\": \"Sumário Executivo\", \"label-analise-laudo\": \"Análise do Laudo\", \"label-verificacao-itens-obrigatorios\": \"Verificação de Itens Obrigatórios Segundo a Instrução Normativa\", \"label-obs-justificativa\": \"Observação/Justificativa\", \"label-consideracoes-finas\": \"Considerações Finais:\", \"label-de-acordo\": \"DE ACORDO\", \"label-chefia-imediata\": \"Chefia Imediata\", \"button-concluir-preenchimento\": \"Concluir Preenchimento\", \"label-nota-tecnica-homologacao\": \"Nota Técnica de Homologação\", \"button-pre-visualizar-nota\": \"Pré-visualizar nota técnica\", \"title-anexo\": \"Anexos\" }");
$templateCache.put("scripts-avaliacao/pages/nota-tecnica/views/assinarNotaTecnicaHomologacaoLaudoAvaliacao.html","<div class=marginModal><div layout=row flex=100><div class=logo-assinar-termo><img width=150px src=assets/img/img-avaliacao/logo_pdf_laudo_avaliacao.png></div><div layout=column class=positionRelative flex=100 style=\"min-height: 160px\"><div><h3 class=alinhamentoTela>{{laudoAvaliacao.orgao}}</h3><h3 class=alinhamentoTela>{{laudoAvaliacao.unidade}}</h3><h3 class=alinhamentoTela>{{laudoAvaliacao.subunidade}}</h3><h2 class=\"negritoTexto alinhamentoTela paddingTop10\">{{\'label-nota-tecnica-homologacao-laudo-avaliacao\' | translate}}{{\' Nº \' + laudoAvaliacao.numeroLaudo}}</h2></div></div></div><div layout=column class=m-t-20><div><span>{{\'label-sumario-executivo\' | translate}}:</span><p class=\"paragraph-padding margin-0 quebrar-linha\"><label ng-bind=notaTecnicaHomologacao.sumarioExecutivo></label></p></div><br><div><span>{{\'label-analise-laudo\' | translate}}:</span><p class=\"paragraph-padding margin-0 quebrar-linha\"><label ng-bind=notaTecnicaHomologacao.analiseLaudo></label></p></div></div><br><br><b>{{\'label-verificacao-itens-obrigatorios-intrucao-normativa\' | translate}}</b><table width=100% class=\"simple-table nota-tecnica-table\"><thead><tr><th>{{\'label-aspectos\' | translate}}</th><th width=10%>{{\'label-verificacao\' | translate}}</th></tr></thead><tbody><tr><td>{{\'label-aspectos-I\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isIdentificacaoPessoaFisica>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isIdentificacaoPessoaFisica>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoIdentificacaoPessoaFisica><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoIdentificacaoPessoaFisica></label></span></td></tr><tr><td>{{\'label-aspectos-II\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isObjetivoFinalidade>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isObjetivoFinalidade>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoObjetivoFinalidade><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoObjetivoFinalidade></label></span></td></tr><tr><td>{{\'label-aspectos-III\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isIdentificacaoCaracterizacao>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isIdentificacaoCaracterizacao>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoIdentificacaoCaracterizacao><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoIdentificacaoCaracterizacao></label></span></td></tr><tr><td>{{\'label-aspectos-IV\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isVerificacaoMetodologiaUtilizada>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isVerificacaoMetodologiaUtilizada>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoVerificacaoMetodologiaUtilizada><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoVerificacaoMetodologiaUtilizada></label></span></td></tr><tr><td>{{\'label-aspectos-V\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isEspecificacaoMetodologia>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isEspecificacaoMetodologia>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoEspecificacaoMetodologia><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoEspecificacaoMetodologia></label></span></td></tr><tr><td>{{\'label-aspectos-VI\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isQualificacaoLegalAssinatura>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isQualificacaoLegalAssinatura>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoQualificacaoLegalAssinatura><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoQualificacaoLegalAssinatura></label></span></td></tr><tr><td colspan=2>{{\'label-aspectos-VII\' | translate}}</td></tr><tr><td><p class=\"paragraph-padding margin-0 quebrar-linha\">{{\'label-aspectos-VII-a\' | translate}}</p></td><td ng-if=notaTecnicaHomologacao.isConferenciaApresentacaoGrafico>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConferenciaApresentacaoGrafico>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConferenciaApresentacaoGrafico><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConferenciaApresentacaoGrafico></label></span></td></tr><tr><td><p class=\"paragraph-padding margin-0 quebrar-linha\">{{\'label-aspectos-VII-b\' | translate}}</p></td><td ng-if=notaTecnicaHomologacao.isConferenciaUtilizacaoTratamento>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConferenciaUtilizacaoTratamento>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConferenciaUtilizacaoTratamento><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConferenciaUtilizacaoTratamento></label></span></td></tr><tr><td><p class=\"paragraph-padding margin-0 quebrar-linha\">{{\'label-aspectos-VII-c\' | translate}}</p></td><td ng-if=notaTecnicaHomologacao.isConferenciaCasoExistencia>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConferenciaCasoExistencia>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConferenciaCasoExistencia><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConferenciaCasoExistencia></label></span></td></tr><tr><td><p class=\"paragraph-padding margin-0 quebrar-linha\">{{\'label-aspectos-VII-d\' | translate}}</p></td><td ng-if=notaTecnicaHomologacao.isConferenciaCoeficienteCorelacao>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConferenciaCoeficienteCorelacao>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConferenciaCoeficienteCorrelacao><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConferenciaCoeficienteCorrelacao></label></span></td></tr><tr><td><p class=\"paragraph-padding margin-0 quebrar-linha\">{{\'label-aspectos-VII-e\' | translate}}</p></td><td ng-if=notaTecnicaHomologacao.isConferenciaCasoCorrelacao>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConferenciaCasoCorrelacao>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConferenciaCasoCorrelacao><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConferenciaCasoCorrelacao></label></span></td></tr><tr><td>{{\'label-aspectos-VIII\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isVerificacaoQuantoRecolhimento>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isVerificacaoQuantoRecolhimento>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoVerificacaoQuantoRecolhimento><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoVerificacaoQuantoRecolhimento></label></span></td></tr><tr><td>{{\'label-aspectos-IX\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isVerificacaoQuantoTratamento>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isVerificacaoQuantoTratamento>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoVerificacaoQuantoTratamento><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoVerificacaoQuantoTratamento></label></span></td></tr><tr><td>{{\'label-aspectos-X\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isConfirmacaoValorDeterminado>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConfirmacaoValorDeterminado>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConfirmacaoValorDeterminado><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConfirmacaoValorDeterminado></label></span></td></tr></tbody></table><br><div><span>{{\'label-conclusao\' | translate}}:</span><p class=\"paragraph-padding margin-0 quebrar-linha\"><label ng-bind=notaTecnicaHomologacao.consideracoesFinais></label></p></div><br><div><span>{{\'label-responsabilidade-tecnica\' | translate}}:</span><p class=\"paragraph-padding margin-0 quebrar-linha\"><label ng-bind=notaTecnicaHomologacao.responsabilidadeTecnica></label></p></div><br><div><span>{{\'label-parecer-tecnico\' | translate}}:</span><p class=\"paragraph-padding margin-0 quebrar-linha\"><label ng-show=notaTecnicaHomologacao.homologacao>Sugiro pela <b>homologação</b> do laudo de avaliação</label><label ng-show=!notaTecnicaHomologacao.homologacao>Sugiro pela <b>não homologação</b> do laudo de avaliação</label></p></div><br><div class=alinhamentoTela ng-repeat=\"homologador in laudoAvaliacao.homologadores\"><span><b>{{homologador.nome}}</b></span><br><span>{{(homologador.registroProfissional == \'CREA\' ? \'CREA: \' : \'CAU: \') + homologador.crea}}</span><br><br></div><br><b>{{\'label-assinaturas\' | translate}}</b><table class=simple-table width=100%><tr ng-repeat=\"homologador in laudoAvaliacao.homologadores\"><td class=alinhamentoTextoCentro><img src=assets/img/img-avaliacao/spu-net-logo-pdf.png></td><td>{{\'label-assinado-eletronicamente\' | translate}} <b><label ng-bind=homologador.nome></label></b>, <b><label ng-bind=\"\'label-responsavel-tecnico\' | translate\"></label></b>, {{\'label-crea-cau\' | translate}}: <b><label ng-bind=homologador.crea></label></b>,<label ng-show=homologador.isAssinado ng-bind=\"(\'label-em\' | translate) + \' \' + (homologador.dataAssinatura | date:\'dd/MM/yyyy\') + \', \' + (\'label-as\' | translate) + \' \' + (homologador.dataAssinatura | date:\'HH:mm\') + \'.\'\"></label></td></tr></table><br><b ng-if=\"notaTecnicaHomologacao.anexos.length > 0\">{{\'title-anexo\' | translate}}</b><br><div layout=column ng-if=\"notaTecnicaHomologacao.anexos.length > 0\"><div layout=column ng-repeat=\"anexo in notaTecnicaHomologacao.anexos\"><br><b>{{\'Anexo \' + ($index+1) + \" - \" + anexo.nome}}</b><div layout=row flex=100><img ng-src=\"{{gerarImagem(anexo.arquivo.binario, anexo.arquivo.contentType)}}\" class=pdfMarginTop ng-if=\"anexo.arquivo.contentType!=\'application/pdf\'\"><iframe ng-src={{anexo.sce}} flex=100 height=500 ng-if=\"anexo.arquivo.contentType==\'application/pdf\'\"></iframe><br></div></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=redirecionarDetalharImovelAvaliacao()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=recusarNotaTecnicaHomologacaoLaudoAvaliacao()><label>{{\'button-recusar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=assinarLaudoRelatorio()><label>{{\'button-assinar\' | translate}}</label></md-button></div></div></div></div>");
$templateCache.put("scripts-avaliacao/pages/nota-tecnica/views/inserirNotaTecnicaHomologacaoLaudoAvaliacao.html","<form name=formularioInserirNotaTecnica><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'label-nota-tecnica-homologacao\' | translate}}</h2></div></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-sumario-executivo\' | translate}}:</label><textarea required class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.sumarioExecutivo name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-analise-laudo\' | translate}}:</label><textarea required class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.analiseLaudo name=\"{{nomeCampo}} - {{\'label-analise-laudo\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina>{{\'label-verificacao-itens-obrigatorios-intrucao-normativa\' | translate}}</h3></div></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th width=40% md-column>{{\'label-aspectos\' | translate}}</th><th width=10% md-column>{{\'label-atende\' | translate}}</th><th width=10% md-column>{{\'label-nao-atende\' | translate}}</th><th width=40% md-column>{{\'label-obs-justificativa\' | translate}}</th></tr></thead><tbody><tr md-row><td md-cell>{{\'label-aspectos-I\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isIdentificacaoPessoaFisica name=\"{{nomeCampo}} - {{\'label-aspectos-I\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isIdentificacaoPessoaFisica name=\"{{nomeCampo}} - {{\'label-aspectos-I\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoIdentificacaoPessoaFisica name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isIdentificacaoPessoaFisica==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-II\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isObjetivoFinalidade name=\"{{nomeCampo}} - {{\'label-aspectos-II\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isObjetivoFinalidade name=\"{{nomeCampo}} - {{\'label-aspectos-II\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoObjetivoFinalidade name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isObjetivoFinalidade==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-III\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isIdentificacaoCaracterizacao name=\"{{nomeCampo}} - {{\'label-aspectos-III\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isIdentificacaoCaracterizacao name=\"{{nomeCampo}} - {{\'label-aspectos-III\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoIdentificacaoCaracterizacao name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isIdentificacaoCaracterizacao==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-IV\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isVerificacaoMetodologiaUtilizada name=\"{{nomeCampo}} - {{\'label-aspectos-IV\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isVerificacaoMetodologiaUtilizada name=\"{{nomeCampo}} - {{\'label-aspectos-IV\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoVerificacaoMetodologiaUtilizada name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isVerificacaoMetodologiaUtilizada==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-V\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isEspecificacaoMetodologia name=\"{{nomeCampo}} - {{\'label-aspectos-V\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isEspecificacaoMetodologia name=\"{{nomeCampo}} - {{\'label-aspectos-V\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoEspecificacaoMetodologia name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isEspecificacaoMetodologia==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-VI\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isQualificacaoLegalAssinatura name=\"{{nomeCampo}} - {{\'label-aspectos-VI\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isQualificacaoLegalAssinatura name=\"{{nomeCampo}} - {{\'label-aspectos-VI\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoQualificacaoLegalAssinatura name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isQualificacaoLegalAssinatura==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-VII\' | translate}}</td></tr><tr md-row><td md-cell>{{\'label-aspectos-VII-a\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConferenciaApresentacaoGrafico name=\"{{nomeCampo}} - {{\'label-aspectos-VII-a\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConferenciaApresentacaoGrafico name=\"{{nomeCampo}} - {{\'label-aspectos-VII-a\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoConferenciaApresentacaoGrafico name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isConferenciaApresentacaoGrafico==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-VII-b\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConferenciaUtilizacaoTratamento name=\"{{nomeCampo}} - {{\'label-aspectos-VII-b\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConferenciaUtilizacaoTratamento name=\"{{nomeCampo}} - {{\'label-aspectos-VII-b\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoConferenciaUtilizacaoTratamento name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isConferenciaUtilizacaoTratamento==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-VII-c\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConferenciaCasoExistencia name=\"{{nomeCampo}} - {{\'label-aspectos-VII-c\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConferenciaCasoExistencia name=\"{{nomeCampo}} - {{\'label-aspectos-VII-c\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoConferenciaCasoExistencia name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isConferenciaCasoExistencia==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-VII-d\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConferenciaCoeficienteCorelacao name=\"{{nomeCampo}} - {{\'label-aspectos-VII-d\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConferenciaCoeficienteCorelacao name=\"{{nomeCampo}} - {{\'label-aspectos-VII-d\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoConferenciaCoeficienteCorrelacao name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isConferenciaCoeficienteCorelacao==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-VII-e\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConferenciaCasoCorrelacao name=\"{{nomeCampo}} - {{\'label-aspectos-VII-e\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConferenciaCasoCorrelacao name=\"{{nomeCampo}} - {{\'label-aspectos-VII-e\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoConferenciaCasoCorrelacao name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isConferenciaCasoCorrelacao==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-VIII\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isVerificacaoQuantoRecolhimento name=\"{{nomeCampo}} - {{\'label-aspectos-VIII\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isVerificacaoQuantoRecolhimento name=\"{{nomeCampo}} - {{\'label-aspectos-VIII\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoVerificacaoQuantoRecolhimento name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isVerificacaoQuantoRecolhimento==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-IX\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isVerificacaoQuantoTratamento name=\"{{nomeCampo}} - {{\'label-aspectos-IX\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isVerificacaoQuantoTratamento name=\"{{nomeCampo}} - {{\'label-aspectos-IX\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoVerificacaoQuantoTratamento name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isVerificacaoQuantoTratamento==false\"></textarea></md-input-container></td></tr><tr md-row><td md-cell>{{\'label-aspectos-X\' | translate}}</td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConfirmacaoValorDeterminado name=\"{{nomeCampo}} - {{\'label-aspectos-X\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=true></md-radio-button></md-radio-group></td><td md-cell><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" required ng-model=notaTecnicaHomologacao.isConfirmacaoValorDeterminado name=\"{{nomeCampo}} - {{\'label-aspectos-X\' | translate}}\"><md-radio-button flex=30 flex-xs=100 flex-sm=100 flex-md=100 class=\"tamanho-radio-button-avaliacao md-primary espacamentoInputs\" ng-value=false></md-radio-button></md-radio-group></td><td md-cell><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><textarea class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.descricaoConfirmacaoValorDeterminado name=\"{{nomeCampo}} - {{\'label-sumario-executivo\' | translate}}\" ng-required=\"notaTecnicaHomologacao.isConfirmacaoValorDeterminado==false\"></textarea></md-input-container></td></tr></tbody></table></md-table-container></div></div><div layout=row layout-xs=column flex-sm=column class=m-t-30><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-conclusao\' | translate}}:</label><textarea required class=barraRolagemTextArea ng-model=notaTecnicaHomologacao.consideracoesFinais name=\"{{nomeCampo}} - {{\'label-consideracoes-finais\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-responsabilidade-tecnica\' | translate}}:</label><input type=text required ng-model=notaTecnicaHomologacao.responsabilidadeTecnica name=responsabilidadeTecnica></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input class=md-block flex=100 flex-xs=100 flex-sm=100><div layout=row><label>{{\'label-parecer-tecnico\' | translate}}:*</label></div><md-radio-group class=tamanho-radio-button-avaliacao ng-model=notaTecnicaHomologacao.homologacao required><div layout=row class=m-t-20><md-radio-button layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=true><div class=texto-preto>Sugiro pela <b>homologação</b> do laudo de avaliação</div></md-radio-button></div><div layout=row><md-radio-button layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=false><div class=texto-preto>Sugiro pela <b>não homologação</b> do laudo de avaliação</div></md-radio-button></div></md-radio-group></md-input></div><documento-laudo-avaliacao-padrao lista-documentos-laudo=notaTecnicaHomologacao.anexos campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_01\'\" titulo=\"\'title-anexos\' | translate\" detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-avaliacao-padrao><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=fecharInserirNotaTecnica()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=visualizarNotaTecnicaHomologacao()><label>{{\'button-pre-visualizar-nota\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=concluirPreenchimentoNotaTecnica(formularioInserirNotaTecnica)><label>{{\'button-concluir-preenchimento\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/nota-tecnica/views/visualizarNotaTecnicaHomologacaoLaudoAvaliacao.html","<div class=marginModal><div layout=row flex=100><div layout=column class=logo-assinar-termo><img width=150px src=assets/img/img-avaliacao/logo_pdf_laudo_avaliacao.png></div><div layout=column class=positionRelative flex=100 style=\"min-height: 160px; padding-left: 150px\"><div><h3 class=alinhamentoTela>{{laudoAvaliacao.orgao}}</h3><h3 class=alinhamentoTela>{{laudoAvaliacao.unidade}}</h3><h3 class=alinhamentoTela>{{laudoAvaliacao.subunidade}}</h3><h2 class=\"negritoTexto alinhamentoTela paddingTop10\">{{\'label-nota-tecnica-homologacao-laudo-avaliacao\' | translate}}{{\' Nº \' + laudoAvaliacao.numeroLaudo}}</h2></div></div></div><div layout=column class=m-t-20><div><span><b>{{\'label-sumario-executivo\' | translate}}:</b></span><p class=\"paragraph-padding margin-0 quebrar-linha\"><label ng-bind=notaTecnicaHomologacao.sumarioExecutivo></label></p></div><br><div><span><b>{{\'label-analise-laudo\' | translate}}:</b></span><p class=\"paragraph-padding margin-0 quebrar-linha\"><label ng-bind=notaTecnicaHomologacao.analiseLaudo></label></p></div></div><br><br><b>{{\'label-verificacao-itens-obrigatorios-intrucao-normativa\' | translate}}</b><table width=100% class=\"simple-table nota-tecnica-table\"><thead><tr><th>{{\'label-aspectos\' | translate}}</th><th width=10%>{{\'label-verificacao\' | translate}}</th></tr></thead><tbody><tr><td>{{\'label-aspectos-I\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isIdentificacaoPessoaFisica>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isIdentificacaoPessoaFisica>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoIdentificacaoPessoaFisica><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoIdentificacaoPessoaFisica></label></span></td></tr><tr><td>{{\'label-aspectos-II\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isObjetivoFinalidade>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isObjetivoFinalidade>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoObjetivoFinalidade><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoObjetivoFinalidade></label></span></td></tr><tr><td>{{\'label-aspectos-III\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isIdentificacaoCaracterizacao>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isIdentificacaoCaracterizacao>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoIdentificacaoCaracterizacao><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoIdentificacaoCaracterizacao></label></span></td></tr><tr><td>{{\'label-aspectos-IV\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isVerificacaoMetodologiaUtilizada>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isVerificacaoMetodologiaUtilizada>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoVerificacaoMetodologiaUtilizada><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoVerificacaoMetodologiaUtilizada></label></span></td></tr><tr><td>{{\'label-aspectos-V\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isEspecificacaoMetodologia>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isEspecificacaoMetodologia>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoEspecificacaoMetodologia><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoEspecificacaoMetodologia></label></span></td></tr><tr><td>{{\'label-aspectos-VI\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isQualificacaoLegalAssinatura>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isQualificacaoLegalAssinatura>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoQualificacaoLegalAssinatura><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoQualificacaoLegalAssinatura></label></span></td></tr><tr><td colspan=2>{{\'label-aspectos-VII\' | translate}}</td></tr><tr><td><p class=\"paragraph-padding margin-0 quebrar-linha\">{{\'label-aspectos-VII-a\' | translate}}</p></td><td ng-if=notaTecnicaHomologacao.isConferenciaApresentacaoGrafico>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConferenciaApresentacaoGrafico>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConferenciaApresentacaoGrafico><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConferenciaApresentacaoGrafico></label></span></td></tr><tr><td><p class=\"paragraph-padding margin-0 quebrar-linha\">{{\'label-aspectos-VII-b\' | translate}}</p></td><td ng-if=notaTecnicaHomologacao.isConferenciaUtilizacaoTratamento>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConferenciaUtilizacaoTratamento>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConferenciaUtilizacaoTratamento><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConferenciaUtilizacaoTratamento></label></span></td></tr><tr><td><p class=\"paragraph-padding margin-0 quebrar-linha\">{{\'label-aspectos-VII-c\' | translate}}</p></td><td ng-if=notaTecnicaHomologacao.isConferenciaCasoExistencia>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConferenciaCasoExistencia>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConferenciaCasoExistencia><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConferenciaCasoExistencia></label></span></td></tr><tr><td><p class=\"paragraph-padding margin-0 quebrar-linha\">{{\'label-aspectos-VII-d\' | translate}}</p></td><td ng-if=notaTecnicaHomologacao.isConferenciaCoeficienteCorelacao>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConferenciaCoeficienteCorelacao>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConferenciaCoeficienteCorrelacao><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConferenciaCoeficienteCorrelacao></label></span></td></tr><tr><td><p class=\"paragraph-padding margin-0 quebrar-linha\">{{\'label-aspectos-VII-e\' | translate}}</p></td><td ng-if=notaTecnicaHomologacao.isConferenciaCasoCorrelacao>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConferenciaCasoCorrelacao>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConferenciaCasoCorrelacao><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConferenciaCasoCorrelacao></label></span></td></tr><tr><td>{{\'label-aspectos-VIII\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isVerificacaoQuantoRecolhimento>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isVerificacaoQuantoRecolhimento>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoVerificacaoQuantoRecolhimento><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoVerificacaoQuantoRecolhimento></label></span></td></tr><tr><td>{{\'label-aspectos-IX\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isVerificacaoQuantoTratamento>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isVerificacaoQuantoTratamento>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoVerificacaoQuantoTratamento><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoVerificacaoQuantoTratamento></label></span></td></tr><tr><td>{{\'label-aspectos-X\' | translate}}</td><td ng-if=notaTecnicaHomologacao.isConfirmacaoValorDeterminado>{{\'label-atende\' | translate}}</td><td ng-if=!notaTecnicaHomologacao.isConfirmacaoValorDeterminado>{{\'label-nao-atende\' | translate}}</td></tr><tr ng-show=notaTecnicaHomologacao.descricaoConfirmacaoValorDeterminado><td colspan=2><b>{{\'label-observacao-justificativa\' | translate}}:</b> <span><label ng-bind=notaTecnicaHomologacao.descricaoConfirmacaoValorDeterminado></label></span></td></tr></tbody></table><br><div><span><b>{{\'label-conclusao\' | translate}}:</b></span><p class=\"paragraph-padding margin-0 quebrar-linha\"><label style=\"text-align: justify-all\" ng-bind=notaTecnicaHomologacao.consideracoesFinais></label></p></div><br><div><span><b>{{\'label-responsabilidade-tecnica\' | translate}}:</b></span><p class=\"paragraph-padding margin-0 quebrar-linha\"><label style=\"text-align: justify-all\" ng-bind=notaTecnicaHomologacao.responsabilidadeTecnica></label></p></div><br><div><span><b>{{\'label-parecer-tecnico\' | translate}}:</b></span><p class=\"paragraph-padding margin-0 quebrar-linha\"><label style=\"text-align: justify-all\" ng-show=notaTecnicaHomologacao.homologacao ng-bind=\"\'label-radio-homologacao\' | translate\"></label><label style=\"text-align: justify-all\" ng-show=!notaTecnicaHomologacao.homologacao ng-bind=\"\'label-radio-nao-homologacao\' | translate\"></label></p></div><br><div class=alinhamentoTela ng-repeat=\"homologador in laudoAvaliacao.homologadores\"><span><b>{{homologador.nome}}</b></span><br><span>{{(homologador.registroProfissional == \'CREA\' ? \'CREA: \' : \'CAU: \') + homologador.crea}}</span><br><br></div><div class=alinhamentoTela ng-show=\"acaoAvaliacao.length > 0\"><span style=\"margin-left: -80%\" ng-show=acaoAvaliacao[0].decisao>{{\'label-de-acordo\' | translate}}</span><br><span style=\"margin-left: -80%\" ng-show=!acaoAvaliacao[0].decisao>{{acaoAvaliacao[0].justificativa}}</span><br><span><b>{{acaoAvaliacao[0].responsavel}}</b></span><br><span>{{\'label-chefia-imediata\' | translate}}</span><br><br></div><br><b>{{\'label-assinaturas\' | translate}}</b><table class=simple-table width=100%><tr ng-repeat=\"homologador in laudoAvaliacao.homologadores\"><td class=alinhamentoTextoCentro><img src=assets/img/img-avaliacao/spu-net-logo-pdf.png></td><td>{{\'label-assinado-eletronicamente\' | translate}} <b><label ng-bind=homologador.nome></label></b>, <b><label ng-bind=\"\'label-responsavel-tecnico\' | translate\"></label></b>, {{\'label-crea-cau\' | translate}}: <b><label ng-bind=homologador.crea></label></b>,<label ng-show=homologador.isAssinado ng-bind=\"(\'label-em\' | translate) + \' \' + (homologador.dataAssinatura | date:\'dd/MM/yyyy\') + \', \' + (\'label-as\' | translate) + \' \' + (homologador.dataAssinatura | date:\'HH:mm\') + \'.\'\"></label></td></tr><tr ng-show=\"acaoAvaliacao.length > 0\"><td class=alinhamentoTextoCentro><img src=assets/img/img-avaliacao/spu-net-logo-pdf.png></td><td>{{\'label-assinado-eletronicamente\' | translate}} <b><label ng-bind=acaoAvaliacao[0].responsavel></label></b>, <b><label ng-bind=\"\'label-chefia\' | translate\"></label></b>,<label ng-bind=\"(\'label-em\' | translate) + \' \' + (acaoAvaliacao[0].dataAssinatura | date:\'dd/MM/yyyy\') + \', \' + (\'label-as\' | translate) + \' \' + (acaoAvaliacao[0].dataAssinatura | date:\'HH:mm\') + \'.\'\"></label></td></tr></table><br><b ng-if=\"notaTecnicaHomologacao.anexos.length > 0\">{{\'title-anexo\' | translate}}</b><br><div layout=column ng-if=\"notaTecnicaHomologacao.anexos.length > 0\"><div layout=column ng-repeat=\"anexo in notaTecnicaHomologacao.anexos\"><br><b>{{\'Anexo \' + ($index+1) + \" - \" + anexo.nome}}</b><div layout=row flex=100><img ng-src=\"{{gerarImagem(anexo.arquivo.binario, anexo.arquivo.contentType)}}\" class=pdfMarginTop ng-if=\"anexo.arquivo.contentType!=\'application/pdf\'\"><iframe ng-src={{anexo.sce}} flex=100 height=500 ng-if=\"anexo.arquivo.contentType==\'application/pdf\'\"></iframe><br></div></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" type=button ng-click=fecharInserirNotaTecnica()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button ng-show=detalharLaudoRelatorio class=\"md-primary md-raised\" type=button ng-click=voltarDetalharLaudo()><label>{{\'button-fechar\' | translate}}</label></md-button></div></div></div></div>");
$templateCache.put("scripts-avaliacao/pages/termo-responsabilidade/language/pt-BR.json","{ \"title-termo-responsabilidade\": \"Termo de Responsabilidade\", \"title-ministerio-planejamento-desenvolvimento-gestao\": \"MINISTÉRIO DO PLANEJAMENTO, DESENVOLVIMENTO E GESTÃO\", \"title-sercretaria-patrimonio-uniao\": \"Secretaria do Patrimônio da Uniao\", \"label-certifico-informcoes-verdadeiras-arquivo-laudo-avaliacao\": \"Certifico que as informações prestadas são verdadeiras e que o arquivo do Laudo/Relatório de Avaliação está correto. O valor atribuído ao imóvel é de inteira responsabilidade do avaliador responsável pela avaliação.\", \"label-certifico-informcoes-verdadeiras-arquivo-nota-tecnica-revalidacao-avaliacao\": \"Certifico que as informações prestadas são verdadeiras e que o arquivo da Nota Técnica de Revalidação do Laudo de avaliação está correto. O valor atribuído ao imóvel é de inteira responsabilidade do avaliador responsável pela avaliação.\", \"label-assinaturas\": \"Assinaturas\", \"label-documento-assinado-por\": \"Documento assinado eletronicamente por\", \"label-cpf-assinador\": \"CPF\", \"btn-fechar\": \"Fechar\", \"btn-assinar\": \"Assinar\" }");
$templateCache.put("scripts-avaliacao/pages/termo-responsabilidade/views/assinarTermoResponsabilidade.html","<div class=marginModal><div layout=row flex=100><div class=logo-assinar-termo><img width=150px src=assets/img/img-avaliacao/logo_pdf_laudo_avaliacao.png></div><div layout=column class=positionRelative flex=100 style=\"min-height: 160px\"><div><h3 class=alinhamentoTela>{{orgao}}</h3><h3 class=alinhamentoTela>{{unidade}}</h3><h3 class=alinhamentoTela>{{subunidade}}</h3><h2 class=\"negritoTexto alinhamentoTela paddingTop10\">{{\'label-termo-responsabilidade\' | translate}}</h2></div></div></div><div layout=row class=m-t-20><label class=marginAuto>{{labelTermoResponsabilidade | translate}}</label></div><div layout=row><h3 class=negritoTexto>{{\'label-assinaturas\' | translate}}</h3></div><div class=pdfEspacamentoCampos><table border=1 style=\"width: 100%\"><tr><td class=alinhamentoTextoCentro><img src=assets/img/img-avaliacao/spu-net-logo-pdf.png></td><td>{{\'label-documento-assinado\' | translate}} por <b><label ng-bind=responsavel.nome></label></b>, {{\'label-cpf\' | translate}}: <b><label ng-bind=\"responsavel.cpf | cpfcnpj\"></label></b>,</td><td ng-show=responsavel.isAssinado><span>{{\'label-assinado-eletronicamente\' | translate}} <b><label ng-bind=\"(responsavel.dataAssinatura | date:\'dd/MM/yyyy\') + \' \' + (\'label-as\' | translate) + \' \' + (responsavel.dataAssinatura | date:\'HH:mm:ss\')\"></label></b></span></td></tr></table></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button ng-show=detalharLaudoRelatorio class=\"md-primary md-raised\" type=button ng-click=voltarTermoResponsabilidadeDetalhar()><label>{{\'btn-fechar\' | translate}}</label></md-button><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" type=button ng-click=voltarTermoResponsabilidade()><label>{{\'btn-fechar\' | translate}}</label></md-button><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised m-l-15\" type=button ng-click=assinarTermoResponsabilidade()><label>{{\'btn-assinar\' | translate}}</label></md-button></div></div></div></div>");
$templateCache.put("scripts-avaliacao/pages/validacao-acoes-avaliacao/language/pt-BR.json","{ \"label-numero-rip\": \"RIP\", \"label-status\": \"Status\", \"label-periodo-entre-data\": \"a\", \"label-tipo-acao\":\"Tipo de Ação\", \"label-tipo-acao-tipo\":\"Tipo\", \"label-benfeitoria-do-imovel\": \"Benfeitoria do Imóvel\", \"label-classificacao-imovel\": \"Classificação do Imóvel\", \"label-cep\": \"CEP\", \"label-ordenar-resultados-por\": \"Ordenar resultados por\", \"label-data\": \"Data\", \"tr-data-acao\": \"Data da Ação\", \"tr-validador\": \"Validador\", \"tr-data-assinatura\": \"Data de Assinatura\", \"label-ordenacao\": \"Ordenação\", \"label-visualizar-historico\": \"Visualizar histórico de ações\", \"label-analisar-acao\": \"Analisar Ação\", \"label-informar-valores-do-laudo\": \"Informar valores do Laudo\", \"label-validacao-de-acoes\": \"Ações da Avaliação\", \"label-validar-acao\": \"Validar Pendência?\", \"label-justificativa\": \"Justificativa\", \"label-historico-validacoes\": \"Histórico de Validações\", \"label-responsaveis_tecnicos\": \"Responsáveis Técnicos\", \"label-pendencia\": \"Dados da Avaliação\", \"label-acao\": \"Ação\", \"label-validacao\": \"Tipo de Validação\", \"label-acao-executada\": \"Ação Executada\", \"label-pendencia-validar\": \"Pendência a Validar\", \"button-gravar-visualizar\": \"Gravar e Visualizar Avaliação\", \"select-avaliacoes-ausente-sem-dados\": \"Sem dados de Avaliação\", \"select-avaliacoes-ausente-sem-laudo\": \"Sem Laudo de Avaliação\", \"select-avaliacoes-ausente-sem-relatorio\": \"Sem Relatório de Valor de Referência\", \"radio-sim\": \"Sim\", \"radio-nao\": \"Não\", \"radio-homologacao\": \"Homologar Laudo de Avaliação N° \", \"radio-nao-homologacao\": \"Não Homologar Laudo de Avaliação N° \", \"label-parecer-tecnico\": \"Parecer Técnico\", \"radio-aceito\": \"Aceito\", \"radio-nao-aceito\": \"Não aceito\", \"tr-tipo\": \"Tipo\", \"tr-acao-executada\": \"Ação Executada\", \"tr-data\": \"Data da Ação\", \"tr-data-assinatura\": \"Data de assinatura\", \"tr-imovel\": \"Imóvel\", \"tr-valor\": \"Valor\", \"tr-responsavel\": \"Responsável pela Ação\", \"tr-status\": \"Status\", \"tr-status-validacao\": \"Status da Validação\", \"tr-decisao\": \"Decisão\", \"tr-nome\": \"Nome\", \"tr-crea-cau\": \"CREA/CAU\", \"tr-validacao\": \"Validação\", \"tr-validador\": \"Validador\", \"tr-data-validacao\": \"Data da Validação\", \"mensagem-erro-gravar-acao\": \"Nenhuma decisão foi selecionada\", \"mensagem-sucesso-dados-gravados\": \"Dados gravados com sucesso\", \"mensagem-erro-data-invalida\": \"A data final não pode ser menor que a data inicial.\" }");
$templateCache.put("scripts-avaliacao/pages/validacao-acoes-avaliacao/views/consultarAcoesAvaliacao.html","<div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'label-validacao-de-acoes\' | translate}}</h2></div></div><div layout=row layout-xs=column layout-sm=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tipo-acao-tipo\' | translate}}:</label><md-select ng-model=filtroConsulta.tipoAcaoAvaliacaoList multiple placeholder=\"{{\'label-tipo-acao-tipo\' | translate}}\"><md-option ng-repeat=\"(chave, valor) in listaTiposAcao\" ng-value=chave>{{valor}}</md-option></md-select></md-input-container><!-- Espaçador entre os campos --><div style=\"width: 15px\"></div><md-input-container class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><label>{{\'label-status\' | translate}}:</label><md-select ng-model=filtroConsulta.tipoStatusAcao name=\"{{nomeCampo}} - {{\'label-status\' | translate}}\" placeholder=\"{{\'label-status\' | translate}}\"><md-option ng-repeat=\"(k,v) in listaStatusAcao\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=33 style=\"margin-top: -15px\"></div><div class=md-block flex-gt-xs flex=33 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'tr-data-acao\' | translate}}:</span></span></div></div><div layout=row layout-xs=column layout-sm=column><md-input-container class=md-block flex=33 flex-xs layout-xs=row layout-sm=row><label>{{\'label-acao-executada\' | translate}}:</label><md-select ng-model=filtroConsulta.tipoAcaoExecutada name=\"{{nomeCampo}} - {{\'label-acao-executada\' | translate}}\" placeholder=\"{{\'label-acao-executada\' | translate}}\"><md-option ng-repeat=\"(k,v) in listaTipoAcaoExecutada\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-container class=m-r-10 flex=33 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><div layout=row layout-xs=row><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-change=validarDatas() ng-model=filtroConsulta.dtInicio md-placeholder=\"{{\'placeholder-data-inicial\' | translate}}\"></md-datepicker></div><div class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><md-datepicker ng-change=validarDatas() ng-disabled=!filtroConsulta.dtInicio ng-model=filtroConsulta.dtFim md-min-date=filtroConsulta.dtInicio md-placeholder=\"{{\'placeholder-data-final\' | translate}}\"></md-datepicker></div></div></md-container><div ng-show=erroDatas style=\"color: red; font-size: 14px; margin-top: 5px\">{{ erroDatas }}</div><md-input-container layout-xs=row flex=33 class=md-block layout-xs=row layout-sm=row><label>{{\'label-numero-rip\' | translate}}:</label><input ng-model=filtroConsulta.ripImovel maxlength=8 name=\"{{nomeCampo}} - {{\'label-numero-rip\' | translate}}\"></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=limparCamposPesquisa()><label>{{\'button-limpar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button id=buttonPesquisarImoveis ng-click=pesquisarAcoesAvaliacao()><label>{{\'button-pesquisar\' | translate}}</label></md-button></div></div></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'tr-tipo\' | translate}}</th><th md-column>{{\'tr-data-assinatura\' | translate}}</th><th md-column>{{\'tr-imovel\' | translate}}</th><th md-column>{{\'tr-valor\' | translate}}</th><th md-column>{{\'tr-acao-executada\' | translate}}</th><th md-column>{{\'tr-responsavel\' | translate}}</th><th md-column>{{\'tr-data-acao\' | translate}}</th><th md-column>{{\'tr-validacao\' | translate}}</th><th md-column>{{\'tr-validador\' | translate}}</th><th md-column>{{\'tr-status-validacao\' | translate}}</th><th md-column>{{\'tr-acao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaAcoes\" ng-if=\"item.laudoAvaliacao == null || item.laudoAvaliacao.situacaoLaudo != \'PENDENTE_HOMOLOGACAO\' || item.laudoAvaliacao.situacaoLaudo != \'EM_PROCESSO_REVALIDACAO\'\"><td md-cell>{{listaTiposAcao[item.tipoAcaoAvaliacao]}}</td><td md-cell><label ng-bind=\"item.laudoAvaliacao.dataAssinatura | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><a href=\"\" ng-click=ripImovelLink(item.ripImovel) target=_blank>{{item.ripImovel}}</a></td><td md-cell><label ng-bind=\"item.valor | currency\"></label></td><td md-cell>{{listaTipoAcaoExecutada[item.tipoAcaoExecutada]}}</td><td md-cell><label ng-bind=item.responsavel></label></td><td md-cell><label ng-bind=\"item.data | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell>{{listaTiposCargo[item.tipoCargo]}}</td><td md-cell><label ng-bind=item.validador></label></td><td md-cell>{{listaStatusAcao[item.tipoStatusAcao]}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=\"\"><md-icon ng-bind=\"\'refresh\'\" ng-click=redirecionarDetalharLaudo(item.laudoAvaliacao)><md-tooltip>{{\'label-visualizar-avaliacao\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"((usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'true\' && item.tipoCargo == \'CHEFIA\') || (usuarioLogado.AVALIACAO_ACOES_TECNICO == \'true\' && item.tipoCargo == \'TECNICO\')) && item.tipoStatusAcao == \'PENDENTE\'\" class=\"md-fab md-raised md-mini\" ng-click=analisarAcaoTecnico(item)><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'label-analisar-acao\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=visualizarHistorico(item.id)><md-icon ng-bind=\"\'search\'\"><md-tooltip>{{\'label-visualizar-historico\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarAcoesAvaliacao md-page-select></md-table-pagination></div></div>");
$templateCache.put("scripts-avaliacao/pages/validacao-acoes-avaliacao/views/validarAcoesAvaliacao.html","<form name=formularioValidarAcoes><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'label-validacao-de-acoes\' | translate}}</h2></div></div><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina>{{\'label-pendencia\' | translate}}</h3></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-tipo\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{listaTiposAcao[acaoAvaliacao.tipoAcaoAvaliacao]}}<md-icon class=mouse-pointer ng-show=acaoAvaliacao.laudoAvaliacao ng-click=redirecionarDetalharLaudo(acaoAvaliacao.laudoAvaliacao) md-font-set=material-icons>search</md-icon><md-icon class=mouse-pointer ng-show=acaoAvaliacao.pvg ng-click=\"redirecionarDetalharPvg(acaoAvaliacao.pvg.id, acaoAvaliacao.pvg.ripImovel)\" md-font-set=material-icons>search</md-icon></label></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-data-assinatura\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{laudoAvaliacao.dataAssinatura | date: \'dd/MM/yyyy\' : \'-0300\'}}</label></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-imovel\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><a href=\"\" ng-click=ripImovelLink(acaoAvaliacao.ripImovel) target=_blank>{{acaoAvaliacao.ripImovel}}</a></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-valor\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{acaoAvaliacao.valor | currency}}</label></div></div><div class=m-t-20 layout=row flex layout-align=\"start start\"></div><div class=m-t-20 layout=row flex layout-align=\"start start\"><h3>{{\'label-responsaveis_tecnicos\' | translate}}:</h3></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th width=50% md-column>{{\'tr-nome\' | translate}}</th><th width=50% md-column ng-show=acaoAvaliacao.laudoAvaliacao>{{\'tr-crea-cau\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"responsavelTecnico in acaoAvaliacao.laudoAvaliacao.responsaveis\" ng-show=acaoAvaliacao.laudoAvaliacao><td md-cell ng-bind=responsavelTecnico.nome></td><td md-cell ng-style=\"responsavelTecnico.crea ? {} : labelVermelho\" ng-bind=\"responsavelTecnico.crea ? responsavelTecnico.crea : \'pendente\'\"></td></tr><tr md-row ng-show=!acaoAvaliacao.laudoAvaliacao><td md-cell ng-bind=acaoAvaliacao.responsavel></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarAcoesAvaliacao md-page-select></md-table-pagination></div></div><div class=m-t-20 layout=row flex layout-align=\"start start\"><h3>{{\'label-historico-validacoes\' | translate}}:</h3></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'tr-acao-executada\' | translate}}</th><th md-column>{{\'tr-responsavel\' | translate}}</th><th md-column>{{\'tr-data\' | translate}}</th><th md-column>{{\'tr-validacao\' | translate}}</th><th md-column>{{\'tr-validador\' | translate}}</th><th md-column>{{\'tr-data-validacao\' | translate}}</th><th md-column>{{\'tr-decisao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaAcoes\" ng-if=\"item.ativo != true\"><td md-cell>{{listaTipoAcaoExecutada[item.tipoAcaoExecutada]}}</td><td md-cell><label ng-bind=item.responsavel></label></td><td md-cell><label ng-bind=\"item.data | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell>{{listaTiposCargo[item.tipoCargo]}}</td><td md-cell><label ng-bind=item.validador></label></td><td md-cell><label ng-bind=\"item.dataAssinatura | dataformatada\"></label></td><td md-cell>{{ item.decisao == null ? \'-\' : (item.decisao == true ? (\'decisao-validacao-aceito\' | translate) : (\'decisao-validacao-nao-aceito\' | translate) + (item.justificativa ? \' - \' + item.justificativa : \'\')) }}</td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarAcoesAvaliacao md-page-select></md-table-pagination></div></div><div layout=row layout-xs=column><div flex><h3 class=\"subtitulo-pagina m-t-20\">{{\'label-pendencia-validar\' | translate}}</h3></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'label-acao-executada\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{listaTipoAcaoExecutada[acaoAvaliacao.tipoAcaoExecutada]}}</label></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-responsavel\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{acaoAvaliacao.responsavel}}</label></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-data\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{acaoAvaliacao.data | date: date: \'dd/MM/yyyy\'}}</label></div></div><div layout=column flex><label class=sidenavTitulos>{{\'label-validar-acao\' | translate}}</label><md-radio-group class=m-t-10 flex class=\"tamanho-radio-button-avaliacao md-block\" ng-required=true ng-model=acaoAvaliacao.decisao ng-change=changeValidarAcao(acaoAvaliacao.decisao)><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=10 ng-value=true><div class=texto-preto>{{\'radio-aceito\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=10 ng-value=false><div class=texto-preto>{{\'radio-nao-aceito\' | translate}}</div></md-radio-button></div></md-radio-group></div><div ng-show=!validar flex=100 layout=row layout-xs=column layout-sm=column><md-input-container class=m-t-20 flex=100 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=acaoAvaliacao.justificativa ng-required=!acaoAvaliacao.decisao></textarea></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" ng-click=redirecionarConsultarAcao()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=salvarAcaoAvaliacao(false) id=botaoGravarAcaoAvaliacao ng-if=\"(usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'true\' && acaoAvaliacao.tipoCargo == \'CHEFIA\') || (usuarioLogado.AVALIACAO_ACOES_TECNICO == \'true\' && acaoAvaliacao.tipoCargo == \'TECNICO\') || usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'true\'\" ng-disabled=\"acaoAvaliacao.decisao === false && !acaoAvaliacao.justificativa\"><label>{{\'button-gravar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=salvarAcaoAvaliacao(true) id=botaoGravarAcaoAvaliacaoVisualizar ng-if=\"(usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'true\' && acaoAvaliacao.tipoCargo == \'CHEFIA\') || (usuarioLogado.AVALIACAO_ACOES_TECNICO == \'true\' && acaoAvaliacao.tipoCargo == \'TECNICO\') || usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'true\'\" ng-disabled=\"acaoAvaliacao.decisao === false && !acaoAvaliacao.justificativa\"><label>{{\'button-gravar-visualizar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/validacao-acoes-avaliacao/views/validarAcoesAvaliacaoSuperintendente.html","<form name=formularioValidarAcoes><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'label-validacao-de-acoes\' | translate}}</h2></div></div><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina>{{\'label-pendencia\' | translate}}</h3></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-tipo\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{listaTiposAcao[acaoAvaliacao.tipoAcaoAvaliacao]}}<md-icon class=mouse-pointer ng-click=\"redirecionarDetalharLaudo(acaoAvaliacao.laudoAvaliacao.id, acaoAvaliacao.laudoAvaliacao.ripImovel)\" md-font-set=material-icons>search</md-icon></label></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-data\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{acaoAvaliacao.data | date: \'dd/MM/yyyy\' : \'-0300\'}}</label></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-imovel\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{acaoAvaliacao.ripImovel}}</label></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-valor\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{acaoAvaliacao.valor | currency}}</label></div></div><div class=m-t-20 layout=row flex layout-align=\"start start\"></div><div class=m-t-20 layout=row flex layout-align=\"start start\"><h3>{{\'label-responsaveis_tecnicos\' | translate}}:</h3></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'tr-nome\' | translate}}</th><th md-column>{{\'tr-crea-cau\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"responsavelTecnico in acaoAvaliacao.laudoAvaliacao.responsaveis\"><td md-cell ng-bind=responsavelTecnico.nome></td><td md-cell ng-style=\"responsavelTecnico.crea ? {} : labelVermelho\" ng-bind=\"responsavelTecnico.crea ? responsavelTecnico.crea : \'pendente\'\"></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarAcoesAvaliacao md-page-select></md-table-pagination></div></div><div class=m-t-20 layout=row flex layout-align=\"start start\"><h3>{{\'label-historico-validacoes\' | translate}}:</h3></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'tr-acao-executada\' | translate}}</th><th md-column>{{\'tr-responsavel\' | translate}}</th><th md-column>{{\'tr-validacao\' | translate}}</th><th md-column>{{\'tr-decisao\' | translate}}</th><th md-column>{{\'tr-data\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaAcoes\" ng-if=\"item.ativo != true\"><td md-cell>{{listaTipoAcaoExecutada[item.tipoAcaoExecutada]}}</td><td md-cell><label ng-bind=item.responsavel></label></td><td md-cell>{{listaTiposCargo[item.tipoCargo]}}</td><td md-cell>{{ item.decisao == null ? \'-\' : (item.decisao == true ? (\'decisao-validacao-aceito\' | translate) : (\'decisao-validacao-nao-aceito\' | translate) + (item.justificativa ? \' - \' + item.justificativa : \'\')) }}</td><td md-cell><label ng-bind=\"item.data | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarAcoesAvaliacao md-page-select></md-table-pagination></div></div><div layout=row layout-xs=column><div flex><h3 class=\"subtitulo-pagina m-t-20\">{{\'label-acao\' | translate}}</h3></div></div><div flex=100 class=m-t-10 layout=row layout-xs=column layout-sm=column><label>{{\'label-parecer-tecnico\' | translate}}:</label></div><div ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == \'DOCUMENTO_SEI\'\" flex=100 class=m-t-10 layout=row layout-xs=column layout-sm=column><label ng-show=laudoAvaliacao.homologacao>Sugiro pela <b>homologação</b> do laudo de avaliação N° {{laudoAvaliacao.numeroLaudo}}</label><label ng-show=!laudoAvaliacao.homologacao>Sugiro pela <b>não homologação</b> do laudo de avaliação N° {{laudoAvaliacao.numeroLaudo}}</label></div><div ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == \'PREENCHER_SISTEMA\'\" flex=100 class=m-t-10 layout=row layout-xs=column layout-sm=column><label ng-show=laudoAvaliacao.notaTecnicaHomologacao.homologacao>Sugiro pela <b>homologação</b> do laudo de avaliação N° {{laudoAvaliacao.numeroLaudo}}</label><label ng-show=!laudoAvaliacao.notaTecnicaHomologacao.homologacao>Sugiro pela <b>não homologação</b> do laudo de avaliação N° {{laudoAvaliacao.numeroLaudo}}</label></div><div ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == \'UPLOAD_ARQUIVO\'\" flex=100 class=m-t-10 layout=row layout-xs=column layout-sm=column><label ng-show=laudoAvaliacao.homologacao>Sugiro pela <b>homologação</b> do laudo de avaliação N° {{laudoAvaliacao.numeroLaudo}}</label><label ng-show=!laudoAvaliacao.homologacao>Sugiro pela <b>não homologação</b> do laudo de avaliação N° {{laudoAvaliacao.numeroLaudo}}</label></div><div class=m-t-10 layout=column flex><label class=sidenavTitulos>{{\'label-validar-acao\' | translate}}</label><md-radio-group class=m-t-10 flex class=\"tamanho-radio-button-avaliacao md-block\" ng-required=true ng-model=acaoAvaliacao.decisao ng-change=changeValidarAcao(acaoAvaliacao.decisao)><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=true><div class=texto-preto>{{\'radio-homologacao\' | translate}}{{laudoAvaliacao.numeroLaudo}}</div></md-radio-button></div><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=false><div class=texto-preto>{{\'radio-nao-homologacao\' | translate}}{{laudoAvaliacao.numeroLaudo}}</div></md-radio-button></div></md-radio-group></div><div ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == \'PREENCHER_SISTEMA\' && acaoAvaliacao.decisao != null && acaoAvaliacao.decisao != laudoAvaliacao.notaTecnicaHomologacao.homologacao\" flex=100 layout=row layout-xs=column layout-sm=column><md-input-container class=m-t-20 flex=100 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=acaoAvaliacao.justificativa ng-required=\"acaoAvaliacao.decisao != laudoAvaliacao.notaTecnicaHomologacao.homologacao\"></textarea></md-input-container></div><div ng-if=\"laudoAvaliacao.tipoNotaTecnicaHomologacao == \'UPLOAD_ARQUIVO\' && acaoAvaliacao.decisao != null && acaoAvaliacao.decisao != laudoAvaliacao.homologacao\" flex=100 layout=row layout-xs=column layout-sm=column><md-input-container class=m-t-20 flex=100 layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=acaoAvaliacao.justificativa ng-required=\"acaoAvaliacao.decisao != laudoAvaliacao.homologacao\"></textarea></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" ng-click=redirecionarConsultarAcao()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=salvarAcaoAvaliacao(false) ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'true\'\"><label>{{\'button-gravar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=salvarAcaoAvaliacao(true) ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'true\'\"><label>{{\'button-gravar-visualizar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/validacao-acoes-avaliacao/views/visualizarHistoricoAcoesAvaliacao.html","<form name=formularioValidarAcoes><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'label-validacao-de-acoes\' | translate}}</h2></div></div><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina>{{\'label-pendencia\' | translate}}</h3></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-tipo\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{listaTiposAcao[acaoAvaliacao.tipoAcaoAvaliacao]}}<md-icon class=mouse-pointer ng-click=redirecionarDetalharLaudo(acaoAvaliacao.laudoAvaliacao) md-font-set=material-icons>search</md-icon></label></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-data-assinatura\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{laudoAvaliacao.dataAssinatura | date: \'dd/MM/yyyy\' : \'-0300\'}}</label></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-imovel\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><a href=\"\" ng-click=ripImovelLink(acaoAvaliacao.ripImovel) target=_blank>{{acaoAvaliacao.ripImovel}}</a></div></div><div style=\"margin-bottom: 10px\" flex=100 layout=row layout-xs=column layout-sm=column><div style=\"margin-right: 10px\" layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label><b>{{\'tr-valor\' | translate}}:</b></label></div><div layout=column flex-xs flex-sm layout-xs=row layout-sm=row><label>{{acaoAvaliacao.valor | currency}}</label></div></div><div class=m-t-20 layout=row flex layout-align=\"start start\"></div><div class=m-t-20 layout=row flex layout-align=\"start start\"><h3>{{\'label-responsaveis_tecnicos\' | translate}}:</h3></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th width=50% md-column>{{\'tr-nome\' | translate}}</th><th width=50% md-column ng-show=acaoAvaliacao.laudoAvaliacao>{{\'tr-crea-cau\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"responsavelTecnico in acaoAvaliacao.laudoAvaliacao.responsaveis\" ng-show=acaoAvaliacao.laudoAvaliacao><td md-cell ng-bind=responsavelTecnico.nome></td><td md-cell ng-style=\"responsavelTecnico.crea ? {} : labelVermelho\" ng-bind=\"responsavelTecnico.crea ? responsavelTecnico.crea : \'pendente\'\"></td></tr><tr md-row ng-show=!acaoAvaliacao.laudoAvaliacao><td md-cell ng-bind=acaoAvaliacao.responsavel></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarAcoesAvaliacao md-page-select></md-table-pagination></div></div><div class=m-t-20 layout=row flex layout-align=\"start start\"><h3>{{\'label-historico-validacoes\' | translate}}:</h3></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'tr-acao-executada\' | translate}}</th><th md-column>{{\'tr-responsavel\' | translate}}</th><th md-column>{{\'tr-data\' | translate}}</th><th md-column>{{\'tr-validacao\' | translate}}</th><th md-column>{{\'tr-validador\' | translate}}</th><th md-column>{{\'tr-data-validacao\' | translate}}</th><th md-column>{{\'tr-decisao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaAcoes\" ng-if=\"item.ativo != true || (item.ativo == true && item.tipoStatusAcao == \'RESOLVIDO\')\"><td md-cell>{{listaTipoAcaoExecutada[item.tipoAcaoExecutada]}}</td><td md-cell><label ng-bind=item.responsavel></label></td><td md-cell><label ng-bind=\"item.data | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell>{{listaTiposCargo[item.tipoCargo]}}</td><td md-cell><label ng-bind=item.validador></label></td><td md-cell><label ng-bind=\"item.dataAssinatura | dataformatada\"></label></td><td md-cell>{{ item.decisao == null ? \'-\' : (item.decisao == true ? (\'decisao-validacao-aceito\' | translate) : (\'decisao-validacao-nao-aceito\' | translate) + (item.justificativa ? \' - \' + item.justificativa : \'\')) }}</td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=pesquisarAcoesAvaliacao md-page-select></md-table-pagination></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" onclick=history.back()><label>{{\'button-fechar\' | translate}}</label></md-button></div></div></div></form>");
$templateCache.put("scripts-avaliacao/pages/valor-edificacao/views/cadastrarValorEdificacao.html","<form name=formularioDados><div layout=row layout-xs=column><div flex><h2 class=titulo-pagina>{{\'label-registrar-valor-edificacao\' | translate}}</h2></div></div><div layout=column flex><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=30><div layout=row><label>{{(tipoTerreno == TIPO_BENFEITORIA ? \'label-valor-benfeitoria\' : \'label-valor-unidade-autonoma\') | translate}}</label></div><input type=text ng-blur=convertNumeroPorExtenso(valor) ng-model=valor name=\"{{nomeCampo}} - Valor Terreno\" ui-number-mask=2 required ng-disabled=detalharLaudoRelatorio></md-input-container><div flex=70><span ng-bind=valorPorExtenso></span></div></div><div layout=row layout-xs=column flex-sm=column><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-descricao-estimativa\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio required class=barraRolagemTextArea ng-model=descricaoEstimativa ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-fonte\' | translate}}\"></textarea></md-input-container></div><div layout=column flex ng-if=\"tipoTerreno == TIPO_BENFEITORIA\"><documento-laudo-relatorio-padrao titulo=\"\'Anexos\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=anexos campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_02 tipo-anexo=\"\'Documentação de Valor Edificação da Benfeitoria \' + codigoBenfeitoria\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=column flex ng-if=\"tipoTerreno != TIPO_BENFEITORIA\"><documento-laudo-relatorio-padrao titulo=\"\'Anexos\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=anexos campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_02 tipo-anexo=\"\'Documentação de Valor Edificação da Unidade Autônoma\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=fecharInserirValorEdificacao()><label>{{\'button-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=salvar(formularioDados) ng-disabled=detalharLaudoAvaliacao><label>{{\'button-salvar\' | translate}}</label></md-button></div></div></div></div></form>");
$templateCache.put("scripts-avaliacao/app/directives/ci-mapa/view/ci-mapa-template.html","<div class=div-mapa><div layout=column class=\"mapa-controles mapa-ferramentas\"><!--<md-button class=\"md-icon-button mapa-botoes-icone \" ng-click=\"alterarVisibilidadeBalaoCamadas();\" ng-if=\"btCamada\">--><!--<ng-md-icon icon=\"layers\" style=\"fill: #fff;\">--><!--<md-tooltip>Camadas</md-tooltip>--><!--</ng-md-icon>--><!--</md-button>--><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=marcarPonto(); ng-if=btMarcar><img src=assets/img/icons/pinPreto.png style=\"color: #fff;height: 25px\"><md-tooltip>Marcar Ponto</md-tooltip></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=selecionarAmostra() ng-if=btSelecionar><ng-md-icon icon=touch_app style=\"fill: #fff\"><md-tooltip>Selecionar Dado de Mercado</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=desenharTerreno(); ng-if=btDesenharPonto><ng-md-icon icon=edit style=\"fill: #fff\"><md-tooltip>Desenhar Terreno</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=desenharUnidadeAutonoma(); ng-if=btDesenharUnidadeAutonoma><ng-md-icon icon=edit style=\"fill: #fff\"><md-tooltip>Desenhar Unidade Autônoma</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=desenharBenfeitoria(); ng-if=btDesenharBenfeitoria><ng-md-icon icon=edit style=\"fill: #fff\"><md-tooltip>Desenhar Benfeitoria</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=apagar() ng-if=btApagar><ng-md-icon icon=delete style=\"fill: #fff\"><md-tooltip>Apagar</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=removerPonto() ng-if=btRemover><ng-md-icon icon=delete style=\"fill: #fff\"><md-tooltip>Remover Ponto Marcado</md-tooltip></ng-md-icon></md-button></div><div class=\"mapa-controles controles-bottom\" layout=row layout-align=\"end end\"><div class=mapa-legenda><div class=mapa-legenda-conteudo style=\"font-size: 11px\" ng-class=\"exibirLegenda ? \'mapa-legenda-conteudo-expandido\': \'\'\" layout=column><div layout=row ng-show=!detalharAmostra><div class=\"div-cor div-amarela\"></div>Dado de mercado que está sendo cadastrado</div><div layout=row ng-show=!detalharAmostra><div class=\"div-cor div-vermelha\"></div>Imóvel da União</div><div layout=row><div class=\"div-cor div-cinza\"></div>Dado de mercado já cadastrado</div></div><md-button class=mapa-legenda-botao ng-click=\"exibirLegenda = !exibirLegenda\" ng-show=!desabilitar>Legenda</md-button></div><div ng-if=showMinimapa class=mapa-minimapa ng-click=alterarBingVisible()><ci-minimapa-avaliacao height={{heightMinimapa}} width={{widthMinimapa}}></ci-minimapa-avaliacao></div></div><div ng-show=listaLayers class=\"mapa-controles mapa-lista-camadas\"><div class=mapa-lista-camadas-title>Camadas</div><div ng-repeat=\"layer in layersTela | filter: layerFilter\"><md-checkbox ng-checked=layer.getVisible(); ng-click=\"alterarLayerVisible(layer.get(\'name\'), $event)\">{{layer.get(\'label\')}}</md-checkbox></div></div><div class=overlayBenfeitoria></div><div id=overlayPesquisarImovel class=overlayMapaDirective ng-click=direcionarDetalharPesquisar(imovelSelecionado); ng-if=\"action === \'pesquisarImovel\' || action === \'detalharImoveisHomologar\'\"></div><div id=overlayLocalizacao ng-if=\"tabIndex === -1\" class=overlayMapaDirective></div><div id=overlayDadosGerais ng-if=\"tabIndex === 0\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayTerreno ng-if=\"tabIndex === 1\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayTabBenfeitoria ng-if=\"tabIndex === 2\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayUnidadeAutonoma ng-if=\"tabIndex === 3\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayInclusaoBenfeitoria ng-if=\"tabIndex === 4\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayProprietario ng-if=\"tabIndex === 5\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayVerLocalizacao ng-if=\"action === \'verlocalizacao\'\" class=overlayMapaDirective ng-click=direcionarDetalharLocalizacao();></div><div id=overlayAmostra class=ol-popup style=\"overflow-y: scroll\" ng-show=\"amostrasSelecionadas.length > 0\"><div ng-repeat=\"amostra in amostrasSelecionadas\"><hr ng-show=\"$index !== 0\"><div layout=row layout-xs=column layout-sm=column><div class=md-block flex-gt-xs flex=30 style=\"display: flex; justify-content: center; flex-direction: column; padding-right: 5px\"><img ng-src=\"{{amostra.fotos.length > 0 ? gerarImagem(amostra.fotos[0].arquivo.binario) :  \'http://i.imgur.com/YV0WS3k.png\'}}\" style=\"max-height: 59px; max-width: 58px\"></div><div class=\"md-block popup-mapa\" flex-gt-xs flex=70><p><b>Dado de mercado: </b></p><p>{{amostra.codigoAmostra}}</p><p><b>Data Atualização:</b></p><p>{{amostra.dtAtualizacao | date:\'dd/MM/yyyy\'}}</p><p><b>Área (m²): </b></p><p>{{amostra.area | number:2}}</p><p><b>Valor (Transação ou Oferta): </b></p><p>{{amostra.valor | currency:\'R$\'}}</p><p><b>Utilizada em: </b></p><p></p></div></div><div class=md-block flex-gt-xs flex=100 ng-if=btAdicionarAmostras><div layout=row layout-align=end><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=removerAmostra(amostra) ng-show=amostra.adicionado><ng-md-icon icon=delete style=\"fill: #fff\"><md-tooltip>Remover</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=adicionarAmostra(amostra) ng-show=!amostra.adicionado><ng-md-icon icon=add_circle style=\"fill: #fff\"><md-tooltip>Adicionar</md-tooltip></ng-md-icon></md-button></div></div><div class=md-block flex-gt-xs flex=100 ng-if=consultarAdicionarAmostras><div layout=row layout-align=center><hr></div><div layout=row layout-align=center><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=cancelar_Amostra(amostra)><ng-md-icon icon=clear style=\"fill: #fff\"><md-tooltip>Remover</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=editar_Amostra(amostra)><ng-md-icon icon=create style=\"fill: #fff\"><md-tooltip>Alterar</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=detalhar_Amostra(amostra)><ng-md-icon icon=search style=\"fill: #fff\"><md-tooltip>Visualizar</md-tooltip></ng-md-icon></md-button></div></div></div></div><div ng-show=bloquearMapa style=\"height: 100%; width: 100%; position: absolute; z-index: 1; background-color: rgba(0,0,0,0.7); top: 0\"><div style=\"display: inline-block; background-color: #ecffe6; border-radius: 20px; margin-left: 6px; padding-right: 5px;  color: #4caf50; position: absolute; top: 100px\"><md-button style=\"margin: 0\" class=\"mapa-botoes-icone md-icon-button\" ng-click=\"desbloquearMapa(\'marcar\');\"><img src=assets/img/icons/pinPreto.png style=\"color: #fff;height: 25px\"><md-tooltip>Marcar Ponto</md-tooltip></md-button>Selecione aqui para marcar o ponto</div><div ng-if=btSelecionar style=\"display: inline-block; background-color: #ecffe6; border-radius: 20px; margin-left: 6px; padding-right: 5px;  color: #4caf50; position: absolute; top: 145px\"><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=\"desbloquearMapa(\'selecionar\')\" ng-if=btSelecionar><ng-md-icon icon=touch_app style=\"fill: #fff\"><md-tooltip>Selecionar Dado de mercado</md-tooltip></ng-md-icon></md-button>Selecione aqui para utilizar um ponto já marcado</div></div></div><style>.ol-popup {\n        position: absolute;\n        background-color: white;\n        -webkit-filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2));\n        filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2));\n        padding: 15px;\n        border-radius: 10px;\n        border: 1px solid #cccccc;\n        bottom: 12px;\n        left: -50px;\n        min-width: 280px;\n    }\n\n    .ol-popup:after, .ol-popup:before {\n        top: 100%;\n        border: solid transparent;\n        content: \" \";\n        height: 0;\n        width: 0;\n        position: absolute;\n        pointer-events: none;\n    }\n\n    .ol-popup:after {\n        border-top-color: white;\n        border-width: 10px;\n        left: 48px;\n        margin-left: -10px;\n    }\n\n    .ol-popup:before {\n        border-top-color: #cccccc;\n        border-width: 11px;\n        left: 48px;\n        margin-left: -11px;\n    }</style>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-avaliacao/templates/areaLateralMapaESlickCarossel.html","<!--<fieldset class=\"fieldset\" style=\"height:320px\">--><!--<legend>{{\'title-localizacao-imovel\' | translate}}</legend>--><!--<mapa-detalhar-imovel-avaliacao ponto-imovel=\"pontoImovel\"--><!--capturar-mapa=\"capturarMapa\"></mapa-detalhar-imovel-avaliacao>--><!--</fieldset>--><fieldset class=fieldset><legend>{{\'title-localizacao-imovel\' | translate}}</legend><label style=\"font-weight: normal\">{{enderecoImovel}}</label><div class=div-cimapa ng-if=carregarMapa><ci-mapa-avaliacao showminimapa=true heightminimapa=50 bt-camada=true widthminimapa=50 tabindex=2 imovel=imovel action=detail habilitar-desenho=true bloquear-mapa=false></ci-mapa-avaliacao></div></fieldset>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-avaliacao/templates/inserirCadastroPvgs.html","<div layout-xs=column><md-content flex><ng-form name=cadastroAvaliacoesPvg><v-accordion multiple class=vAccordion--default><v-pane expanded=false><v-pane-header style=\"margin-top:15px !important\" class=textoAccordionPvgLaudo>{{\'title-valor-dominio-pleno\' | translate}}</v-pane-header><v-pane-content><div layout=column flex><apresentar-valor-dominio-pleno rip=rip></apresentar-valor-dominio-pleno></div></v-pane-content></v-pane><v-pane><v-pane-header class=textoAccordionPvgLaudo>{{\'title-avaliacao-terreno-pvg-spu\' | translate}}</v-pane-header><v-pane-content><div layout=column flex><adicionar-avaliacao-pvg-spu rip=rip imovel=imovel area-terreno=areaTerreno></adicionar-avaliacao-pvg-spu></div></v-pane-content></v-pane><v-pane><v-pane-header class=textoAccordionPvgLaudo>{{\'title-avaliacao-terreno-referencia-valor-venal-municipio\' | translate}}</v-pane-header><v-pane-content><div layout=column flex><adicionar-avaliacao-pvg-municipio rip=rip area-terreno=areaTerreno uf=imovel.enderecamento.uf></adicionar-avaliacao-pvg-municipio></div></v-pane-content></v-pane><v-pane><v-pane-header class=\"textoAccordionPvgLaudo teste\">{{\'title-avaliacao-terreno-referencia-incra\' | translate}}</v-pane-header><v-pane-content><div layout=column flex><adicionar-avaliacao-pvg-incra rip=rip area-terreno=areaTerreno uf=imovel.enderecamento.uf></adicionar-avaliacao-pvg-incra></div></v-pane-content></v-pane></v-accordion></ng-form></md-content></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-avaliacao/templates/inserirDocumentosLaudoAvaliacao.html","<div layout=row class=tituloLaudoAvaliacao><label><b>{{\'title-laudo-avaliacao-relatorio-referencia\' | translate}}</b></label></div><fieldset layout=column layout-xs=column class=paddingFieldset><div layout=row layout-align=\"start center\" layout-xs=column layout-align-xs=\"start start\" layout-sm=column layout-align-sm=\"start start\" layout-md=column layout-align-md=\"start start\"><div class=paddingCamposFieldset layout=column flex=35 flex-xs=100 flex-sm=100 style=\"margin-right: 10px\"><md-input-container class=\"md-block semMargem\"><label class=sidenavTitulos>{{\'label-nome-arquivo\' | translate}}*:</label><input ng-model=documentoLaudoRelatorio type=text ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=column flex=20 class=semMargem><md-actions class=\"selecionarArquivoClass md-block sizeSelecionarArquivo\"><md-button class=\"semMargem btn-size-big btn-avaliacao\" ng-disabled=detalharLaudoRelatorio ngf-multiple=false ngf-select=adicionarArquivoDocumentoLaudo($files)><label>{{\'button-selecionar-arquivo\' | translate}}</label></md-button></md-actions></div><div layout=column flex=30 class=\"semMargem paddingCamposFieldset\"><md-input-container class=\"md-block semMargem\"><label class=sidenavTitulos>{{\'label-documento\' | translate}}:</label><input type=text ng-model=arquivoTemp.nome disabled></md-input-container></div><div layout=column flex=15 class=\"semMargem selecionarArquivoClass\"><md-actions style=\"padding-left: 0px !important\"><md-button class=\"semMargem btn-size-medium btn-avaliacao\" style=\"padding-left: 15px;padding-right: 15px\" ng-click=adicionarListaDocumentoLaudoRelatorio() ng-disabled=detalharLaudoRelatorio><label>{{\'button-adicionar-arquivo\' | translate}}</label></md-button></md-actions></div></div><div flex class=caixa-tabela-resultado><div layout=column ng-show=\"laudoAvaliacao.arquivosLaudo.length > 0\"><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column width=40%>{{\'label-nome\' |translate}}</th><th md-column width=40%>{{\'tr-arquivo\' |translate}}</th><th md-column width=20%>{{\'tr-acoes\' |translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documentoLaudo in paginationDocumentos\"><td md-cell>{{documentoLaudo.nomeArquivo}}</td><td md-cell><a href=\"\" ng-click=downloadDocumento(documentoLaudo.arquivo.id)>{{documentoLaudo.arquivo.nome}}</a></td><td md-cell><div layout=row layout-align=\"center center\"><a href=\"\" ng-click=\"removerDocumentoLaudoRelatorio($event, $index, documentoLaudo.arquivo.id)\"><ng-md-icon icon=clear style=fill:#788489 size=22 alt=\"Excluir Documento\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoDocumentosLaudo md-page-select></md-table-pagination></md-conent></div></div></fieldset>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-avaliacao/templates/inserirLaudoDadosUnidadeAutonoma.html","<div layout-xs=column><md-content flex><ng-form name=forms.pvgForm><input type=hidden ng-model=hasPontoImovel name=pontoImovel><v-accordion multiple class=vAccordion--default><v-pane><v-pane-header style=\"margin-top:15px !important; margin-bottom: 10px\" class=textoAccordionPvgLaudo>{{\'title-dados-laudo-avaliacao\' | translate}}</v-pane-header><v-pane-content><dados-laudo-avaliacao rip=rip uf=uf></dados-laudo-avaliacao></v-pane-content></v-pane><v-pane><v-pane-header style=\"margin-top:15px !important; margin-bottom: 10px\" class=textoAccordionPvgLaudo>{{\'title-dados-relatorio-valor-referencia\' | translate}}</v-pane-header><v-pane-content><dados-relatorio-valor-referencia rip=rip uf=uf></dados-relatorio-valor-referencia></v-pane-content></v-pane><!-- Exibir este trecho apenas quando for ambiente de desenvolvimento--><v-pane ng-if=ambienteDev ng-style=\"ambienteDev ? {\'background-color\': \'rgb(249, 242, 214)\'} : {}\"><v-pane-header style=\"margin-top:15px !important; margin-bottom: 10px\" class=textoAccordionPvgLaudo>{{\'title-dados-laudo-avaliacao-locacao\' | translate}}</v-pane-header><v-pane-content><dados-laudo-avaliacao-locacao rip=rip uf=uf></dados-laudo-avaliacao-locacao></v-pane-content></v-pane><v-pane ng-show=!dadosAvaliacao.isLaudoAquisicao ng-if=false><v-pane-header class=textoAccordionPvgLaudo>{{\'title-dados-aquisicao-onerosa\' | translate}}</v-pane-header><v-pane-content>Em construção</v-pane-content></v-pane></v-accordion></ng-form></md-content></div>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/anexos.tmpl.html","<ng-form name=formularioValidacao><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header class=textoAccordionPvgLaudo>{{\'title-anexos-da-avaliacao\' | translate}}</v-pane-header><v-pane-content><documento-laudo-relatorio-padrao ng-show=!detalharLaudoRelatorio titulo=\"\'Documentos complementares do \' + tipo\" detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=anexo.documentosComplementares campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" tipo-anexo=\"\'Anexos\'\" anexo-laudo-relatorio=anexo></documento-laudo-relatorio-padrao><div layout=row flex flex-xs ng-show=\"anexo.anexos.length > 0\"><h3 class=textoAccordionPvgLaudo>Anexos do {{tipo}}</h3></div><div layout=row layout-align=\"center start\" ng-show=\"anexo.anexos.length > 0\"><div flex><md-table-container><table md-table><thead md-head md-order=order md-on-reorder=pesquisarDadosAmostra><tr md-row style=\"color: black !important\"><th md-column width=30px></th><th md-column>Arquivos a serem anexados na {{tipo}}</th></tr></thead><!----><tbody md-body ng-repeat=\"row in anexo.anexos | orderBy: row.ordem\" ng-if=\"row.filhos.length > 0 &&\n                                                                                      ((identificacao.metodologia == \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\' &&\n                                                                                      (row.nome != \'Arquivos de Orçamento\' && row.nome != \'Relatórios Estatísticos\' && row.nome != \'Orçamento/Fonte de Custos\')) ||\n                                                                                      ((identificacao.metodologia == \'INFERENCIA_ESTATISTICA\' || identificacao.metodoAvaliacao == \'COMPARATIVO_MERCADO\') &&\n                                                                                      (row.nome != \'Arquivos de Orçamento\' && row.nome != \'Orçamento/Fonte\' && row.nome != \'Orçamento/Fonte de Custos\' &&\n                                                                                      row.nome != \'Fonte BDI\' && row.nome != \'Fonte Depreciação\' && row.nome != \'Avaliação - Anexos\' && row.nome != \'Fonte Fator de Comercialização\')) ||\n                                                                                      ((identificacao.quantificacaoCustoBenfeitoria == \'ORCAMENTO\') &&\n                                                                                      (row.nome != \'Orçamento/Fonte\' && row.nome != \'Orçamento/Fonte de Custos\' && row.nome != \'Avaliação - Anexos\')) ||\n                                                                                      ((identificacao.quantificacaoCustoBenfeitoria == \'CUB_PROJETO_SEMELHANTE\') &&\n                                                                                      (row.nome != \'Orçamento/Fonte\' && row.nome != \'Arquivos de Orçamento\' && row.nome != \'Avaliação - Anexos\'))) ||\n                                                                                      row.nome==\'Memorial Descritivo do Imóvel\'\"><tr md-row><td md-cell width=30px class=anexo-column-acao><md-button ng-disabled=detalharLaudoRelatorio class=\"md-fab md-raised md-mini anexo-button-acao anexo-button-acao-up\" ng-click=\"anexo.anexos = decrementarOrdem(anexo.anexos, row.ordem)\" ng-show=\"row.ordem >= 3\"><md-icon ng-bind=\"\'expand_less\'\"></md-icon></md-button><md-button ng-disabled=detalharLaudoRelatorio class=\"md-fab md-raised md-mini anexo-button-acao anexo-button-acao-down\" ng-click=\"anexo.anexos = incrementarOrdem(anexo.anexos, row.ordem)\" ng-show=\"row.ordem < qtdOrdem && row.ordem > 1\"><md-icon ng-bind=\"\'expand_more\'\"></md-icon></md-button></td><td md-cell><md-checkbox ng-disabled=detalharLaudoRelatorio class=\"full-width md-primary\" ng-model=row.selecionado aria-label={{row.nome}}><label style=\"color: black !important\">{{row.nome}}</label></md-checkbox></td></tr><tr md-row ng-repeat=\"filho in row.filhos\" ng-show=row.selecionado><td md-cell width=30px class=anexo-column-acao><md-button ng-disabled=detalharLaudoRelatorio class=\"md-fab md-raised md-mini anexo-button-acao anexo-button-acao-up anexo-button-acao-son\" ng-click=\"row.filhos = decrementarOrdemFilhos(row.filhos, filho.ordem)\" ng-show=\"filho.ordem != 0\"><md-icon ng-bind=\"\'expand_less\'\"></md-icon></md-button><md-button ng-disabled=detalharLaudoRelatorio class=\"md-fab md-raised md-mini anexo-button-acao anexo-button-acao-down anexo-button-acao-son\" ng-click=\"row.filhos = incrementarOrdemFilhos(row.filhos, filho.ordem)\" ng-show=\"filho.ordem != (row.filhos.length - 1)\"><md-icon ng-bind=\"\'expand_more\'\"></md-icon></md-button></td><td md-cell style=\"padding-left: 55px !important\"><md-checkbox ng-disabled=detalharLaudoRelatorio class=\"full-width md-primary\" ng-model=filho.selecionado aria-label={{filho.nome}}><label style=\"color: black !important\">{{filho.nome}}</label><span ng-if=filho.arquivo.id>/ <a ng-href={{baixarArquivo(filho.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=filho.arquivo.nome></label></a></span><span ng-if=filho.imagemBenfeitoria.id>/ <a ng-href=\"{{baixarImagem(filho.imagemBenfeitoria.id, \'IMAGEM_BENFEITORIA\')}}\" target=_blank><label class=mouse-pointer ng-bind=filho.imagemBenfeitoria.nomeArquivo></label></a></span><span ng-if=filho.imagemImovel.id>/ <a ng-href=\"{{baixarImagem(filho.imagemImovel.id, \'IMAGEM_IMOVEL\')}}\" target=_blank><label class=mouse-pointer ng-bind=filho.imagemImovel.nomeImagem></label></a></span><span ng-if=filho.imagemUnidadeAutonoma.id>/ <a ng-href=\"{{baixarImagem(filho.imagemUnidadeAutonoma.id, \'IMAGEM_UNIDADE_AUTONOMA\')}}\" target=_blank><label class=mouse-pointer ng-bind=filho.imagemUnidadeAutonoma.nomeOriginal></label></a></span><span ng-if=filho.documentoEdificacao.arquivo.id>/ <a ng-href={{baixarArquivo(filho.documentoEdificacao.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=filho.documentoEdificacao.arquivo.nomeOriginal></label></a></span></md-checkbox></td></tr><!--<tr md-row>--><!--<td md-cell width=\"100%\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"docCartorial\" ng-change=\"changeDocumentoCartorial(docCartorial)\" aria-label=\"Documento Cartorial\">--><!--Documento Cartorial--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"documentoCartorial in anexos.documentosCartoriais.listaDocumentosCartoriais\" ng-show=\"mostraDocumentosCartoriais\">--><!--<td md-cell style=\"padding-left: 54px !important;\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"documentoCartorial.id\">--><!--<label>{{documentoCartorial.nome}} /</label>--><!--<a ng-href=\"{{baixarArquivo(documentoCartorial.arquivo.id)}}\" target=\"_blank\">--><!--<label class=\"mouse-pointer\" ng-bind=\"documentoCartorial.arquivo.nome\"></label>--><!--</a>--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row>--><!--<td md-cell>--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"docUrbanistico\" ng-change=\"changeParametrosUrbanisticos(docUrbanistico)\" aria-label=\"Documento Parâmetros Urbanísticos\">--><!--Documento Parâmetros Urbanísticos--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"parametroUrbanistico in anexos.parametrosUrbanisticos.listaParametrosUrbanisticos\" ng-show=\"mostraParametrosUrbanisticos\">--><!--<td md-cell style=\"padding-left: 54px !important;\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"parametroUrbanistico.id\">--><!--<label>{{parametroUrbanistico.nome}} /</label>--><!--<a ng-href=\"{{baixarArquivo(parametroUrbanistico.arquivo.id)}}\" target=\"_blank\">--><!--<label class=\"mouse-pointer\" ng-bind=\"parametroUrbanistico.arquivo.nome\"></label>--><!--</a>--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"documento in documentacaoBenfeitoria\">--><!--<td md-cell>--><!--<md-checkbox class=\"full-width md-primary\" ng-change=\"changeBenfeitoria(docBenfeitoria)\" ng-model=\"docBenfeitoria\" aria-label=\"Documentação de Edificação da Benfeitoria\">--><!--Documentação de Edificação da Benfeitoria--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"documento in listaDocEdificacaoBenfeitoria\" ng-show=\"mostrarDocBenfeitorias\">--><!--<td md-cell style=\"padding-left: 54px !important;\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"documento.id\">--><!--<label>{{documento.nome}} /</label>--><!--<a ng-href=\"{{baixarArquivo(documento.arquivo.id)}}\" target=\"_blank\">--><!--<label class=\"mouse-pointer\" ng-bind=\"documento.arquivo.nome\"></label>--><!--</a>--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row>--><!--<td md-cell>--><!--<md-checkbox class=\"full-width md-primary\" ng-change=\"changeUnidadeAutonoma(docUnidadeAutonoma)\" ng-model=\"docUnidadeAutonoma\" aria-label=\" Documentação de Edificação da Unidade Autônoma\">--><!--Documentação de Edificação da Unidade Autônoma--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"documento in listaDocEdificacaoAutonoma\" ng-show=\"mostrarDocAutonoma\">--><!--<td md-cell style=\"padding-left: 54px !important;\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"documento.id\" ng-disabled=\"desabilitaUnidadeAutonoma\">--><!--<label>{{documento.nome}} /</label>--><!--<a ng-href=\"{{baixarArquivo(documento.arquivo.id)}}\" target=\"_blank\">--><!--<label class=\"mouse-pointer\" ng-bind=\"documento.arquivo.nome\"></label>--><!--</a>--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row>--><!--<td md-cell>--><!--<md-checkbox class=\"full-width md-primary\" ng-change=\"changeFotoVistoria(fotoVistoria)\" ng-model=\"fotoVistoria\" aria-label=\"Fotos da Vistoria\">--><!--Fotos da Vistoria--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"documento in listaFotoVistoria\" ng-show=\"mostrarFotoVistoria\">--><!--<td md-cell style=\"padding-left: 54px !important;\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"documento.id\">--><!--<label>{{documento.nome}} /</label>--><!--<a ng-href=\"{{baixarArquivo(documento.arquivo.id)}}\" target=\"_blank\">--><!--<label class=\"mouse-pointer\" ng-bind=\"documento.arquivo.nome\"></label>--><!--</a>--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row>--><!--<td md-cell>--><!--<md-checkbox class=\"full-width md-primary\" ng-change=\"changeFotoImovel(fotoImovel)\" ng-model=\"fotoImovel\" aria-label=\"Fotos do Imóvel\">--><!--Fotos do Imóvel--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"documento in listaFotoImovel\" ng-show=\"mostrarFotoVistoria\">--><!--<td md-cell style=\"padding-left: 54px !important;\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"documento.id\">--><!--<label>{{documento.nome}} /</label>--><!--<a ng-href=\"{{baixarArquivo(documento.arquivo.id)}}\" target=\"_blank\">--><!--<label class=\"mouse-pointer\" ng-bind=\"documento.arquivo.nome\"></label>--><!--</a>--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row>--><!--<td md-cell>--><!--<md-checkbox class=\"full-width md-primary\" ng-change=\"changeAvaliacao(avaliacaoAnexo)\" ng-model=\"avaliacaoAnexo\" aria-label=\"Avaliação - Anexos\">--><!--Avaliação - Anexos--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"documento in listaAvaliacaoAnexo\" ng-show=\"mostrarAvaliacaoAnexo\">--><!--<td md-cell style=\"padding-left: 54px !important;\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"documento.id\" ng-disabled=\"desabilitaAnexosAvaliacao\">--><!--<label>{{documento.nome}} /</label>--><!--<a ng-href=\"{{baixarArquivo(documento.arquivo.id)}}\" target=\"_blank\">--><!--<label class=\"mouse-pointer\" ng-bind=\"documento.arquivo.nome\"></label>--><!--</a>--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row>--><!--<td md-cell>--><!--<md-checkbox class=\"full-width md-primary\" ng-change=\"changeAvaliacao(amostrasFoto)\" ng-model=\"amostrasFoto\" aria-label=\"Amostras - Foto\">--><!--Amostras - Foto--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"documento in listaAmostrasFoto\" ng-show=\"mostrarAmostrasFoto\">--><!--<td md-cell style=\"padding-left: 54px !important;\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"documento.id\">--><!--<label>{{documento.nome}} /</label>--><!--<a ng-href=\"{{baixarArquivo(documento.arquivo.id)}}\" target=\"_blank\">--><!--<label class=\"mouse-pointer\" ng-bind=\"documento.arquivo.nome\"></label>--><!--</a>--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row>--><!--<td md-cell>--><!--<md-checkbox class=\"full-width md-primary\" ng-change=\"changeRelEstatisticos(relEstatisticos)\" ng-model=\"relEstatisticos\" aria-label=\"Relatórios Estatísticos\">--><!--Relatórios Estatísticos--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"documento in listaRelEstatisticos\" ng-show=\"mostrarRelEstatisticos\">--><!--<td md-cell style=\"padding-left: 54px !important;\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"documento.id\">--><!--<label>{{documento.nome}} /</label>--><!--<a ng-href=\"{{baixarArquivo(documento.arquivo.id)}}\" target=\"_blank\">--><!--<label class=\"mouse-pointer\" ng-bind=\"documento.arquivo.nome\"></label>--><!--</a>--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row>--><!--<td md-cell>--><!--<md-checkbox class=\"full-width md-primary\" ng-change=\"changeAnexos(anexos)\" ng-model=\"anexos\" aria-label=\"Anexos\">--><!--Anexos--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row ng-repeat=\"documento in listaAnexos\" ng-show=\"mostrarAnexos\">--><!--<td md-cell style=\"padding-left: 54px !important;\">--><!--<md-checkbox class=\"full-width md-primary\" ng-model=\"documento.id\">--><!--<label>{{documento.nome}} /</label>--><!--<a ng-href=\"{{baixarArquivo(documento.arquivo.id)}}\" target=\"_blank\">--><!--<label class=\"mouse-pointer\" ng-bind=\"documento.arquivo.nome\"></label>--><!--</a>--><!--</md-checkbox>--><!--</td>--><!--</tr>--><!--<tr md-row>--><!--<td md-cell>--><!--<md-checkbox class=\"full-width\" ng-change=\"changeMemorialDescritivo(memorialDescritivo)\" ng-model=\"memorialDescritivo\" aria-label=\"Memorial Descritivo do Imóvel\" ng-disabled=\"desabilitaMemorialDescritivo\">--><!--Memorial Descritivo do Imóvel--><!--</md-checkbox>--><!--</td>--><!--</tr>--></tbody></table></md-table-container></div></div></v-pane-content></v-pane></v-accordion></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/avaliacao.tmpl.html","<ng-form name=formularioValidacao><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header class=textoAccordionPvgLaudo>{{\'title-dados-da-avaliacao\' | translate}}</v-pane-header><v-pane-content><div layout=column flex><fieldset class=fieldsetArredondado layout=column layout-xs=column ng-if=\"identificacao.metodologia != \'OUTRO\'\"><div class=upload-arquivos><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-pressupostos\' | translate}}</h3></div><div class=upload-body><div layout=row layout-xs=column><md-input-container class=md-block flex flex-xs flex-sm><label>{{\'title-pressuposto\' | translate}}</label><md-select ng-model=documento.pressuposto placeholder=\"{{\'title-pressuposto\' | translate}}\" ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"(k,v) in listaPressupostos\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50><div layout=row><label>Descrição</label></div><input type=text ng-model=documento.descricao placeholder=Descrição ng-disabled=detalharLaudoRelatorio></md-input-container></div><div layout=row layout-xs=column><md-container class=md-block flex=50 layout=column flex-xs flex-sm layout-xs=row layout-sm=row ng-class=\"formEnviado && !documento.dtArquivo ? \'md-input-invalid\' : \'\'\"><md-datepicker ng-model=documento.dtArquivo md-max-date=dataAtual md-placeholder=\"{{\'placeholder-data\' | translate}}*\" class=\"datePickerClass m-b-25\" ng-disabled=detalharLaudoRelatorio></md-datepicker></md-container><div class=md-block flex-gt-xs flex=50><md-button class=\"md-primary md-raised upload-button\" type=button ng-disabled=\"documento.arquivo.nome || detalharLaudoRelatorio\" ngf-multiple=false ngf-select=adicionarArquivoDocumento($files)><label>{{customButtonLabel ? customButtonLabel : \'button-selecionar-arquivo\' | translate}}</label></md-button></div></div></div><div layout=row layout-xs=column ng-show=!detalharLaudoRelatorio><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised add-button\" type=button ng-click=adicionarListaDocumentos(formularioDocumentos) ng-disabled=detalharLaudoAvaliacao><label>{{\'button-adicionar-arquivo\' | translate}}</label></md-button></div></div></div></div><fieldset class=fieldsetArredondado layout=column layout-xs=column><br><div layout=row flex flex-xs><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-linearidade\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.linearidade ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-linearidade\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column class=upload-table ng-show=mostrarLinearidade><div flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 40%\" md-column>Descrição</th><th style=\"width: 10%\" md-column>Data</th><th style=\"width: 40%\" md-column>Arquivo</th><th ng-show=!detalharLaudoRelatorio style=\"width: 5%\" md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in avaliacao.documentosPressupostos\" ng-if=\"documento.pressuposto == \'LINEARIDADE\'\"><td md-cell><label ng-bind=documento.descricao>{{pressuposto.type}}</label></td><td md-cell><label ng-bind=\"documento.dtArquivo | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><a ng-href={{baixarArquivo(documento.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=documento.arquivo.nome></label></a></td><td md-cell width=50px ng-show=!detalharLaudoRelatorio><a ng-click=\"documento.$edit = true; removerDocumento(documento);\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></div></div></fieldset><fieldset class=fieldsetArredondado layout=column layout-xs=column><br><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-normalidade\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.normalidade ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-normalidade\' | translate}}\"></textarea></md-input-container><div layout=row layout-xs=column class=upload-table ng-show=mostrarNormalidade><div flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 40%\" md-column>Descrição</th><th style=\"width: 10%\" md-column>Data</th><th style=\"width: 40%\" md-column>Arquivo</th><th ng-show=!detalharLaudoRelatorio style=\"width: 5%\" md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in avaliacao.documentosPressupostos |  filter:{ pressuposto: \'NORMALIDADE\' }\"><td md-cell><label ng-bind=documento.descricao>{{pressuposto.type}}</label></td><td md-cell><label ng-bind=\"documento.dtArquivo | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><a ng-href={{baixarArquivo(documento.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=documento.arquivo.nome></label></a></td><td md-cell width=50px ng-show=!detalharLaudoRelatorio><a ng-click=\"documento.$edit = true; removerDocumento(documento);\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></div></div></fieldset><fieldset class=fieldsetArredondado layout=column layout-xs=column><br><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-homocedasticidade\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.homocedasticidade ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-homocedasticidade\' | translate}}\"></textarea></md-input-container><div layout=row layout-xs=column class=upload-table ng-show=mostrarHomocedasticidade><div flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 40%\" md-column>Descrição</th><th style=\"width: 10%\" md-column>Data</th><th style=\"width: 40%\" md-column>Arquivo</th><th ng-show=!detalharLaudoRelatorio style=\"width: 5%\" md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in avaliacao.documentosPressupostos |  filter:{ pressuposto: \'HOMOCEDASTICIDADE\' }\"><td md-cell><label ng-bind=documento.descricao style=\"word-break: break-all\">{{pressuposto.type}}</label></td><td md-cell><label ng-bind=\"documento.dtArquivo | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><a ng-href={{baixarArquivo(documento)}} target=_blank><label class=mouse-pointer ng-bind=documento.arquivo.nome></label></a></td><td md-cell width=50px ng-show=!detalharLaudoRelatorio><a ng-click=\"documento.$edit = true; removerDocumento(documento);\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></div></div></fieldset><fieldset class=fieldsetArredondado layout=column layout-xs=column><br><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-verificacao-autocorrelacao\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo!=\'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.verificacaoAutocorrelacao ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-verificacao-autocorrelacao\' | translate}}\"></textarea></md-input-container><div layout=row layout-xs=column class=upload-table ng-show=mostrarVerificacao><div flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 40%\" md-column>Descrição</th><th style=\"width: 10%\" md-column>Data</th><th style=\"width: 40%\" md-column>Arquivo</th><th ng-show=!detalharLaudoRelatorio style=\"width: 5%\" md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in avaliacao.documentosPressupostos |  filter:{ pressuposto: \'VERIFICACAO_DE_AUTOCORRELACAO\' }\"><td md-cell><label ng-bind=documento.descricao style=\"word-break: break-all\">{{pressuposto.type}}</label></td><td md-cell><label ng-bind=\"documento.dtArquivo | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><a ng-href={{baixarArquivo(documento.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=documento.arquivo.nome></label></a></td><td md-cell width=50px ng-show=!detalharLaudoRelatorio><a ng-click=\"documento.$edit = true; removerDocumento(documento);\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></div></div></fieldset><fieldset class=fieldsetArredondado layout=column layout-xs=column><br><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-colinearidade-multicolinearidade\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.colinearidadeMulticolinearidade ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-colinearidade-multicolinearidade\' | translate}}\"></textarea></md-input-container><div layout=row layout-xs=column class=upload-table ng-show=mostrarColinearidade><div flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 40%\" md-column>Descrição</th><th style=\"width: 10%\" md-column>Data</th><th style=\"width: 40%\" md-column>Arquivo</th><th ng-show=!detalharLaudoRelatorio style=\"width: 5%\" md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in avaliacao.documentosPressupostos |  filter:{ pressuposto: \'COLINEARIDADE_OU_MULTICOLINEARIDADE\' }\"><td md-cell><label ng-bind=documento.descricao style=\"word-break: break-all\">{{pressuposto.type}}</label></td><td md-cell><label ng-bind=\"documento.dtArquivo | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><a ng-href={{baixarArquivo(documento.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=documento.arquivo.nome></label></a></td><td md-cell width=50px ng-show=!detalharLaudoRelatorio><a ng-click=\"documento.$edit = true; removerDocumento(documento);\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></div></div></fieldset><fieldset class=fieldsetArredondado layout=column layout-xs=column><br><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-pontos-influenciantes\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.pontosInfluenciantes ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-pontos-influenciantes\' | translate}}\"></textarea></md-input-container><div layout=row layout-xs=column class=upload-table ng-show=\"mostrarPontos \"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 40%\" md-column>Descrição</th><th style=\"width: 10%\" md-column>Data</th><th style=\"width: 40%\" md-column>Arquivo</th><th ng-show=!detalharLaudoRelatorio style=\"width: 5%\" md-column>Ações</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in avaliacao.documentosPressupostos |  filter:{ pressuposto: \'PONTOS_INFLUENCIANTES_OU_OUTLIERS\' }\"><td md-cell><label ng-bind=documento.descricao style=\"word-break: break-all\">{{pressuposto.type}}</label></td><td md-cell><label ng-bind=\"documento.dtArquivo | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><a ng-href={{baixarArquivo(documento.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=documento.arquivo.nome></label></a></td><td md-cell width=50px ng-show=!detalharLaudoRelatorio><a ng-click=\"documento.$edit = true; removerDocumento(documento);\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></div></div></fieldset></fieldset><div class=m-t-10 layout=row layout-xs=column><md-input-container flex flex-xs flex-sm class=md-block><label>{{\'title-ressalvas-fatores\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.ressalvasFatoresLimitantes ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-ressalvas-fatores\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container flex flex-xs flex-sm class=md-block><label>{{\'title-diagnostico-de-mercado\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.diagnosticoMercado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-diagnostico-de-mercado\' | translate}}\"></textarea><md-tooltip md-direction=top>Conforme item 7.7.2 da NBR 14.653-1</md-tooltip></md-input-container></div><div layout=row layout-xs=column><md-input-container flex flex-xs flex-sm class=md-block><label>{{\'title-liquidez-do-imovel\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.liquidezImovel ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-liquidez-do-imovel\' | translate}}\"></textarea><md-tooltip md-direction=top>Ex.: baixa, média, alta, etc.</md-tooltip></md-input-container></div><div layout=row layout-xs=column><md-input-container flex flex-xs flex-sm class=md-block><label>{{\'title-pesquisa-de-mercado\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.pesquisaMercado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-pesquisa-de-mercado\' | translate}}\"></textarea></md-input-container></div><div ng-if=\"identificacao.metodologia == \'OUTRO\'\"><div layout=row layout-xs=column><md-input-container flex flex-xs flex-sm class=md-block><label>{{\'label-descricao-metodologia-utilizada\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.metodologia == \'OUTRO\'\" class=barraRolagemTextArea ng-model=avaliacao.descricao ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-metodologia-utilizada\' | translate}}\"></textarea></md-input-container></div><div layout=column flex><documento-laudo-relatorio-padrao detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=avaliacao.anexos campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_02 titulo=\"\'Fonte/Material Utilizado\'\"></documento-laudo-relatorio-padrao></div></div><div layout=row flex flex-xs ng-show=\"identificacao.tipo == \'LAUDO\'\"><label class=textoAccordionPvgLaudo>{{\'title-pressupostos-modelo\' | translate}} {{ferramentasAnaliticas[ferramentaAnalitica]}}</label></div><md-input-container layout-xs=row flex-xs flex-sm class=md-block ng-if=\"identificacao.tipo == \'LAUDO\'\"><label>{{\'title-ferramenta-utilizada\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo == \'LAUDO\'\" class=barraRolagemTextArea ng-model=avaliacao.ferramentaUtilizada ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-ferramenta-utilizada\' | translate}}\"></textarea><md-tooltip md-direction=top>informar software utilizado</md-tooltip></md-input-container></div></v-pane-content></v-pane><v-pane expanded=true ng-if=\"identificacao.tipo == \'LAUDO\' || identificacao.metodologia == \'INFERENCIA_ESTATISTICA\'\"><v-pane-header class=\"textoAccordionPvgLaudo teste\">{{\'title-variaveis-amostra\' | translate}}</v-pane-header><v-pane-content><div layout=column flex><variavel-amostra avaliacao=avaliacao variaveis=avaliacao.variaveis amostras=avaliacao.amostras variaveis-amostras=avaliacao.variaveisAmostras redirecionar-selecionar-amostras=redirecionarSelecionarAmostras redirecionar-adicionar-variavel-amostras=redirecionarAdicionarVariavelAmostras detalhar-laudo-relatorio=detalharLaudoRelatorio salvar-como-rascunho-laudo-relatorio=salvarComoRascunhoLaudoRelatorio tipo-laudo=identificacao.tipo is-benfeitoria-edificacao=isBenfeitoriaEdificacao is-benfeitoria-sem-edificacao=isBenfeitoriaSemEdificacao rip=identificacao.rip is-unidade-autonoma=isUnidadeAutonoma></variavel-amostra></div></v-pane-content></v-pane></v-accordion></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/avaliacaoEstimativa.tmpl.html","<ng-form name=formularioValidacao><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header class=textoAccordionPvgLaudo>{{\'title-dados-da-avaliacao\' | translate}}</v-pane-header><v-pane-content><div class=m-t-20 layout=column flex><div layout=row><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-ressalvas-fatores\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.ressalvasFatoresLimitantes ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-ressalvas-fatores\' | translate}}\"></textarea></md-input-container></div><div layout=row><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-diagnostico-de-mercado\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.diagnosticoMercado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-diagnostico-de-mercado\' | translate}}\">\n                        </textarea><md-tooltip md-direction=top>Conforme item 7.7.2 da NBR 14.653-1</md-tooltip></md-input-container></div><div layout=row><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-liquidez-do-imovel\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.liquidezImovel ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-liquidez-do-imovel\' | translate}}\">\n                        </textarea><md-tooltip md-direction=top>Ex.: baixa, média, alta, etc.</md-tooltip></md-input-container></div></div><div><fieldset class=fieldsetArredondado layout=column layout-xs=column ng-if=\"(listaBenfeitoriaAvaliacao && listaBenfeitoriaAvaliacao.length > 0) || (unidadeAutonomaImovel)\"><div layout=column flex><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label>{{\'label-fonte-valor-benfeitorias\' | translate}}:*</span></div></div><md-radio-group ng-disabled=detalharLaudoRelatorio flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=avaliacao.tipoValorLaudo ng-change=changeTipoValorLaudo(avaliacao.tipoValorLaudo) name=\"{{nomeCampo}} - {{\'label-fonte-valor-benfeitorias\' | translate}}\" required><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=20 ng-value=CUB><div class=texto-preto>{{\'radio-cub\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=20 ng-value=OUTRA_FONTE><div class=texto-preto>{{\'radio-outra-fonte\' | translate}}</div></md-radio-button></div></md-radio-group></div><div ng-if=\"avaliacao.tipoValorLaudo == CUB\" layout=column flex class=\"m-b-20 m-t-20\"><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=quantificacaoCusto.tipoArea name=\"{{nomeCampo}} - {{\'title-area\' | translate}}\" ng-change=limparBenfeitoriaCub();><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposArea\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div flex class=caixa-tabela-resultado ng-if=\"quantificacaoCusto.tipoArea == \'AREA_REAL\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-cub\' | translate}}*</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs><md-select ng-disabled=detalharLaudoRelatorio ng-model=benfeitoria.cubDepreciacao required ng-change=calcularCustoConstrucao();><md-option ng-repeat=\"cub in tiposCub\" ng-value=cub.nome>{{cub.nome}}</md-option></md-select></td></tr></tbody></table></md-table-container></md-content></div></div><div flex class=caixa-tabela-resultado ng-if=\"quantificacaoCusto.tipoArea == \'AREA_EQUIVALENTE_CONSTRUCAO\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area-equivalente\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-cub-padrao\' | translate}}</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.areaEquivalente.areaEquivalenteConstrucao | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.areaEquivalente.cubPadrao}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-show=\"benfeitoria.areaEquivalente.areaEquivalenteConstrucao && !detalharLaudoRelatorio\" ng-click=\"calcularAreaEquilente(benfeitoria, false)\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-show=\"!benfeitoria.areaEquivalente.areaEquivalenteConstrucao && !detalharLaudoRelatorio\" ng-click=\"calcularAreaEquilente(benfeitoria, false)\"><md-icon ng-bind=\"\'add\'\"><md-tooltip>{{\'label-calcular-area-equivalente\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-show=benfeitoria.areaEquivalente.areaEquivalenteConstrucao ng-click=\"calcularAreaEquilente(benfeitoria, true)\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>{{\'label-detalhar\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container></md-content></div></div><div class=m-t-20 layout=row layout-xs=row flex=100 flex-xs flex-sm><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=avaliacao.isEstimacaoCustos name=\"{{\'title-estimacao-custos\' | translate}}\"><label class=subtitulo-pagina>{{\'title-estimacao-custos\' | translate}}</label><md-tooltip md-direction=right>{{\'mensagem-adicionar-itens-nao-previstos-cub\' | translate}}</md-tooltip></md-checkbox></div><div ng-if=avaliacao.isEstimacaoCustos><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=quantificacaoCusto.tipoEstimativaCusto name=\"{{nomeCampo}} - {{\'title-estimacao-custos\' | translate}}\" ng-change=\"\"><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposEstimativaCusto\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div flex class=caixa-tabela-resultado ng-if=quantificacaoCusto.tipoEstimativaCusto><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 80%\" class=th-margin-pvgs md-column></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs ng-if=\"quantificacaoCusto.tipoEstimativaCusto == \'UTILIZAR_ORCAMENTOS\'\"><table class=no-border md-table><tr class=no-border md-row><td class=no-border style=\"width: 40%\" md-cell>{{\'label-orcamento-elevadores\' | translate}}</td><td class=no-border style=\"width: 60%\" md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=\"md-block m-t-5 m-r-10\"><label ng-bind=\"\'R$\'\"></label></div><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoElevadores ui-mask-placeholder-char=space ui-number-mask=2></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"(benfeitoria.valorOrcamentoElevadores > 0 ? convertNumeroPorExtenso(benfeitoria.valorOrcamentoElevadores) : \'\')\"></label></div></div></td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-instalacoes-especiais\' | translate}}</td><td class=no-border md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=\"md-block m-t-5 m-r-10\"><label ng-bind=\"\'R$\'\"></label></div><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoInstalacoesEspeciais ui-mask-placeholder-char=space ui-number-mask=2></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"(benfeitoria.valorOrcamentoInstalacoesEspeciais > 0 ? convertNumeroPorExtenso(benfeitoria.valorOrcamentoInstalacoesEspeciais) : \'\')\"></label></div></div></td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-fundacoes-especiais\' | translate}}</td><td class=no-border md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=\"md-block m-t-5 m-r-10\"><label ng-bind=\"\'R$\'\"></label></div><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoFundacoesEspeciais ui-mask-placeholder-char=space ui-number-mask=2></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"(benfeitoria.valorOrcamentoFundacoesEspeciais > 0 ? convertNumeroPorExtenso(benfeitoria.valorOrcamentoFundacoesEspeciais) : \'\')\"></label></div></div></td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-fundacoes-diretas\' | translate}}</td><td class=no-border md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=\"md-block m-t-5 m-r-10\"><label ng-bind=\"\'R$\'\"></label></div><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoFundacoesDiretas ui-mask-placeholder-char=space ui-number-mask=2></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"(benfeitoria.valorOrcamentoFundacoesDiretas > 0 ? convertNumeroPorExtenso(benfeitoria.valorOrcamentoFundacoesDiretas) : \'\')\"></label></div></div></td></tr></table></td><td md-cell class=th-margin-pvgs ng-if=\"quantificacaoCusto.tipoEstimativaCusto == \'APLICAR_PERCENTUAL\'\"><table class=no-border md-table><tr class=no-border md-row><td class=no-border style=\"width: 40%\" md-cell>{{\'label-elevadores\' | translate}}</td><td class=no-border style=\"width: 60%\" md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoElevadores ui-mask-placeholder-char=space ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"\'%\'\"></label></div></div></td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-instalacoes-especiais\' | translate}}</td><td class=no-border md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoInstalacoesEspeciais ui-mask-placeholder-char=space ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"\'%\'\"></label></div></div></td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-fundacoes-especiais\' | translate}}</td><td class=no-border md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoFundacoesEspeciais ui-mask-placeholder-char=space ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"\'%\'\"></label></div></div></td></tr></table></td></tr></tbody></table></md-table-container></md-content></div></div><div layout=column class=m-t-10 flex><documento-laudo-relatorio-padrao titulo=\"\'Orçamentos/Fonte\'\" lista-documentos-laudo=quantificacaoCusto.arquivosOrcamento campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Orçamento/Fonte\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=column class=m-t-20><h3 class=subtitulo-pagina>{{\'title-custo-construcao\' | translate}}</h3></div><div flex class=caixa-tabela-resultado style=\"margin-top: -10px\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-custo-construcao\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-estimativa\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorCustoConstrucao | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{((benfeitoria.area * benfeitoria.valorCustoConstrucao) | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container></md-content></div></div></div><div class=m-t-20 layout=row layout-xs=row flex=100 flex-xs flex-sm><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=avaliacao.isBdi name=\"{{\'title-bdi\' | translate}}\"><label class=subtitulo-pagina>{{\'title-bdi\' | translate}}</label><md-tooltip md-direction=right>{{\'mensagem-adicionar-bdi\' | translate}}</md-tooltip></md-checkbox></div><div ng-if=avaliacao.isBdi><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=quantificacaoCusto.tipoBdi name=\"{{nomeCampo}} - {{\'title-bdi\' | translate}}\" ng-change=\"\"><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposDbi\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div ng-if=quantificacaoCusto.tipoBdi><div layout=column class=m-t-20 flex ng-if=\"quantificacaoCusto.tipoBdi != \'ARBITRADO\'\"><documento-laudo-relatorio-padrao titulo=\"\'Fonte BDI\'\" lista-documentos-laudo=quantificacaoCusto.arquivosFonteBdi campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Fonte BDI\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-descricao-bdi-utilizado\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"quantificacaoCusto.tipoBdi != \'ARBITRADO\'\" class=barraRolagemTextArea ng-model=quantificacaoCusto.descricaoBdi ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-bdi-utilizado\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex=30 flex-xs flex-sm class=md-block><label>{{\'label-taxa-bdi\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-change=calcularValorBenfeitoria(); ng-model=quantificacaoCusto.valorTaxaBdi ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div></div></div><div class=m-t-20 layout=row layout-xs=row flex=100 flex-xs flex-sm><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=avaliacao.isDepreciacao name=\"{{\'title-depreciacao\' | translate}}\"><label class=subtitulo-pagina>{{\'title-depreciacao\' | translate}}</label></md-checkbox></div><div ng-if=avaliacao.isDepreciacao><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=quantificacaoCusto.tipoDepreciacao name=\"{{nomeCampo}} - {{\'title-depreciacao\' | translate}}\" ng-change=limparDadosBenfeitoriaDepreciacao();><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposDepreciacao\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div ng-if=quantificacaoCusto.tipoDepreciacao><div layout=column class=m-t-20 flex ng-if=\"quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\'\"><documento-laudo-relatorio-padrao titulo=\"\'Fonte Depreciação\'\" lista-documentos-laudo=quantificacaoCusto.arquivosFonteDepreciacao campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Fonte Depreciacao\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{(quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\' ? \'label-descricao-depreciacao-calculada\' : \'label-descricao-depreciacao-arbitrada\') | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\'\" class=barraRolagemTextArea ng-model=quantificacaoCusto.descricaoDepreciacao ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-depreciacao\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><md-input-container layout-xs=row flex=30 flex-xs flex-sm class=md-block><label>{{\'label-taxa-depreciacao\' | translate}}:<md-tooltip md-direction=top>Percentual a ser descontado do custo de reprodução</md-tooltip></label><input ng-disabled=detalharLaudoRelatorio ng-change=calcularValorBenfeitoria(); required ng-model=quantificacaoCusto.valorTaxaDepreciacao ui-mask-placeholder-char=space ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div><div flex class=caixa-tabela-resultado ng-if=\"quantificacaoCusto.tipoDepreciacao == \'ROSS_HEIDECKE\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-vida-util\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-idade-aparente\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column><label>{{\'label-estado-conservacao\' | translate}}</label><md-icon style=cursor:pointer ng-click=showDialogDepreciacaoHeidecke($event) flex=10 ng-bind=\"\'info\'\" class=\"md-fab md-raised md-mini\"><md-tooltip>Depreciação de Heidecke</md-tooltip></md-icon></th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-taxa-depreciacao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=1 max=999 ng-model=benfeitoria.vidaUtil ng-change=calcularValorEstimado();calcularValorBenfeitoria(); required name=\"{{nomeCampo}} - {{\'label-vida-util-ano\' | translate}}\"></td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=0 max=999 ng-model=benfeitoria.idadeAparente ng-change=calcularValorEstimado();calcularValorBenfeitoria(); required name=\"{{nomeCampo}} - Idade Aparente\"></td><td md-cell class=th-margin-pvgs><md-select ng-disabled=detalharLaudoRelatorio ng-model=benfeitoria.estadoConservacao required ng-change=\"calcularValorEstimado(); calcularValorBenfeitoria();\"><md-option ng-repeat=\"item in listaEstadosConservacao\" ng-value=item>{{item.nome}}</md-option></md-select></td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorTaxaDepreciacao | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row flex flex-xs ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><label class=textoAccordionPvgLaudo>{{\'label-informacoes-contabilidade\' | translate}}</label></div><div flex ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 33%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 33%\" class=th-margin-pvgs md-column>{{\'label-vida-util\' | translate}}*</th><th style=\"width: 33%\" class=th-margin-pvgs md-column>{{\'label-idade-aparente\' | translate}}*</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=1 max=999 ng-model=benfeitoria.vidaUtil ng-change=\"\" required name=\"{{nomeCampo}} - {{\'label-vida-util-ano\' | translate}}\"></td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=0 max=999 ng-model=benfeitoria.idadeAparente ng-change=\"\" required name=\"{{nomeCampo}} - Idade Aparente\"></td></tr></tbody></table></md-table-container></md-content></div></div><div ng-if=\"avaliacao.isBdi || avaliacao.isDepreciacao\"><div class=m-t-20 layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-valor-benfeitorias\' | translate}}</h3></div><div class=m-b-30 ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=md-block flex-gt-xs flex=100><span class=datepicker-label>{{benfeitoria.codigo + \'-\' + benfeitoria.nome}}:</span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><label>{{(benfeitoria.valorCalculado | currency).replace(\'R$\', \'\')}}</label></div><div flex=70 class=md-block><label ng-bind=\"(benfeitoria.valorCalculado ? benfeitoria.formatterValorCalculadoBenfeitoria : \'\')\"></label></div></div></div><div class=m-b-30><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=md-block flex-gt-xs flex=100><span class=datepicker-label>{{\'label-custo-total\' | translate}}:</span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><label>{{(valorCustoTotalBenfeitoria | currency).replace(\'R$\', \'\')}}</label></div><div flex=70 class=md-block><label ng-bind=\"(valorCustoTotalBenfeitoria ? formatterValorCustoTotalBenfeitoria : \'\')\"></label></div></div></div></div></div></div></div><div ng-if=\"avaliacao.tipoValorLaudo == OUTRA_FONTE && listaBenfeitoriaAvaliacao && listaBenfeitoriaAvaliacao.length > 0\" layout=column flex class=m-t-10><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-descricao-fonte\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"avaliacao.tipoValorLaudo == OUTRA_FONTE\" class=barraRolagemTextArea ng-model=avaliacao.descricaoFonte ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-fonte\' | translate}}\"></textarea></md-input-container></div><table-informar-valor-edificacao ng-if=\"avaliacao.tipoValorLaudo == OUTRA_FONTE && listaBenfeitoriaAvaliacao && listaBenfeitoriaAvaliacao.length > 0\" nome-campo=nomeCampo benfeitorias-imovel=listaBenfeitoriaAvaliacao detalhar-laudo-relatorio=detalharLaudoRelatorio redirecionar-informar-valor-edificacao=redirecionarInformarValorEdificacao avaliacao=avaliacao valor-adotado=valorAdotadoSelecionado tipo-valor-laudo=avaliacao.tipoValorLaudo valor-terreno=valorTerreno total-valor-terreno=totalValorBenfeitorias moeda=avaliacao.resultadosAvaliacao.moeda lista-moedas=listaMoedas></table-informar-valor-edificacao><div layout=row layout-xs=column class=m-b-20><div layout=column flex=75></div><div layout=column flex=25 ng-if=\"avaliacao.tipoValorLaudo == OUTRA_FONTE || avaliacao.tipoValorLaudo == CUB\"><label class=\"sidenavTitulos marginTopHomologadorNome\" layout=row>{{\'label-valor-estimado-total\' | translate}} {{moeda ? moeda.simbolo : listaMoedas[0].simbolo}}: {{(valorTotalTerreno | currency).replace(\'R$\',\'\')}}</label></div></div></div></fieldset><div layout=column flex><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-fonte-valor-terreno\' | translate}}:*</span></span></div></div><md-radio-group ng-disabled=detalharLaudoRelatorio flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=avaliacao.tipoValorTerrenoAvaliacao ng-change=changeTipoValorTerrenoAvaliacao(avaliacao.tipoValorTerrenoAvaliacao) name=\"{{nomeCampo}} - {{\'label-fonte-valor-terreno\' | translate}}\" required><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=20 ng-value=PVG_SPU><div class=texto-preto>{{\'radio-pvg-spu\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=20 ng-value=PVG_MUNICIPIO><div class=texto-preto>{{\'radio-valor-municipio\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=20 ng-value=PVG_INCRA><div class=texto-preto>{{\'radio-incra\' | translate}}</div></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=20 ng-value=OUTRA_FONTE><div class=texto-preto>{{\'radio-outra-fonte\' | translate}}</div></md-radio-button></div></md-radio-group><div layout=row layout-xs=column class=m-t-10 ng-show=showValorTerreno><label flex=100><span>{{\'title-valor-terreno\' |translate}}:</span> <span ng-bind=\"(pvg.valorTerreno | currency).replace(\'R$\', \'\')\"></span></label></div><div layout=row layout-xs=column class=m-t-10 ng-show=showValorTerreno><label flex=100><span>{{\'title-valor-m2\' |translate}}:</span> <span ng-bind=\"(pvg.valor | currency).replace(\'R$\', \'\')\"></span></label></div><div layout=row layout-xs=column class=\"m-t-10 m-b-20\" ng-show=showValorTerreno><label flex=100><span>{{\'title-periodo-avaliacao\' |translate}}:</span> <span ng-bind=\"(pvg.dataInicial | date : \'dd/MM/yyyy\')\"></span></label></div><div ng-if=\"avaliacao.tipoValorTerrenoAvaliacao == OUTRA_FONTE\"><div class=m-t-20 layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=30><div layout=row><label>{{\'title-valor-terreno\' | translate}}</label></div><input type=text ng-blur=\"valorTerrenoPorExtenso = convertNumeroPorExtenso(avaliacao.valorTerreno)\" ng-model=avaliacao.valorTerreno name=\"{{nomeCampo}} - Valor Terreno\" ui-number-mask=2 ng-required=\"identificacao.metodologia != \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\' || avaliacao.tipoValorTerrenoAvaliacao == OUTRA_FONTE\" ng-disabled=detalharLaudoRelatorio></md-input-container><div flex=70><span ng-bind=valorTerrenoPorExtenso></span></div></div><div class=m-b-20 layout=row layout-xs=column ng-show=avaliacao.valorTerreno><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>Valor m²: {{((avaliacao.valorTerreno/areaTotalTerreno) | currency).replace(\'R$\', \'\')}}</span></span></div></div><div layout=row layout-xs=column flex-sm=column><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-fonte\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.metodologia != \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\' || avaliacao.tipoValorTerrenoAvaliacao == OUTRA_FONTE\" class=barraRolagemTextArea ng-model=avaliacao.fonteValor ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-fonte\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=33 style=\"margin-top: -15px\"><span class=datepicker-label>{{\'label-data\' | translate}}:*</span></div></div><div layout=row layout-xs=column flex=100 flex-xs=100><div flex=33 layout=column><md-input class=md-block><md-datepicker ng-disabled=detalharLaudoRelatorio md-max-date=dataAtual flex-xs=100 flex-sm flex-md ng-model=avaliacao.dataFonteValor md-placeholder=\"{{\'label-data\' | translate}}\" ng-required=\"avaliacao.tipoValorTerrenoAvaliacao == OUTRA_FONTE\" ng-change=\"\"></md-datepicker></md-input></div></div></div><div layout=column flex ng-if=\"avaliacao.tipoValorTerrenoAvaliacao == OUTRA_FONTE\"><documento-laudo-relatorio-padrao detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=avaliacao.anexos campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_02 tipo-anexo=\"\'Avaliação - Anexos\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div></div></div></v-pane-content></v-pane></v-accordion></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/avaliacaoGlebaUrbanizavel.tmpl.html","<ng-form name=formularioValidacao><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header class=textoAccordionPvgLaudo>{{\'title-dados-da-avaliacao\' | translate}}</v-pane-header><v-pane-content><div class=m-t-20 layout=column flex><div layout=row><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-ressalvas-fatores\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.ressalvasFatoresLimitantes ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-ressalvas-fatores\' | translate}}\"></textarea></md-input-container></div><div layout=row><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-diagnostico-de-mercado\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.diagnosticoMercado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-diagnostico-de-mercado\' | translate}}\">\n                        </textarea><md-tooltip md-direction=top>Conforme item 7.7.2 da NBR 14.653-1</md-tooltip></md-input-container></div><div layout=row><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-liquidez-do-imovel\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"identificacao.tipo != \'RELATORIO\'\" class=barraRolagemTextArea ng-model=avaliacao.liquidezImovel ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-liquidez-do-imovel\' | translate}}\">\n                        </textarea><md-tooltip md-direction=top>Ex.: baixa, média, alta, etc.</md-tooltip></md-input-container></div></div><div><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label>{{\'label-modelo\' | translate}}:*</span></div></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=avaliacao.modeloAvaliacaoGleba name=\"{{nomeCampo}} - {{\'label-modelo\' | translate}}\" ng-change=changeModeloAvaliacao()><div layout=row flex=100><div layout=column flex=30 style=position:relative ng-repeat=\"(k,v) in listaModelos\"><md-radio-button ng-disabled=detalharLaudoRelatorio class=md-primary ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button><md-icon ng-click=\"showDialogCalculoModelosGleba($event, k)\" style=\"position: absolute\" ng-style=\"k == \'JOSE_BARBAGELATA\' ? marginLeft143 : k == \'HELIO_CAIRES\' ? marginLeft130 : marginLeft112\" ng-bind=\"\'info\'\" class=\"mouse-pointer md-mini\"></md-icon></div></div></md-radio-group></md-input></div><fieldset class=fieldsetArredondado layout=column layout-xs=column ng-show=\"avaliacao.modeloAvaliacaoGleba != null\"><div layout=row layout-xs=column class=m-t-20><h3 class=subtitulo-pagina ng-show=\"avaliacao.modeloAvaliacaoGleba == \'HELIO_CAIRES\'\">{{\'title-helio-caires\' | translate}}</h3><h3 class=subtitulo-pagina ng-show=\"avaliacao.modeloAvaliacaoGleba == \'JOSE_BARBAGELATA\'\">{{\'title-jose-barbagelata\' | translate}}</h3><h3 class=subtitulo-pagina ng-show=\"avaliacao.modeloAvaliacaoGleba == \'OSCAR_OLAVE\'\">{{\'title-oscar-olave\' | translate}}</h3></div><div ng-show=\"avaliacao.modeloAvaliacaoGleba == \'HELIO_CAIRES\'\"><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=100 class=md-block><label>{{\'label-area-imovel\' | translate}}: {{(areaTotalTerreno | currency:\'\':4).replace(\'R$\', \'\')}}</label></div></div></div><div ng-if=\"avaliacao.modeloAvaliacaoGleba == \'JOSE_BARBAGELATA\' || avaliacao.modeloAvaliacaoGleba == \'OSCAR_OLAVE\'\"><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=100 class=md-block><label>{{\'label-area-imovel\' | translate}}: {{(areaTotalTerreno | currency:\'\':4).replace(\'R$\', \'\')}}</label></div></div><div class=m-t-20 layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><label>{{\'label-perdas-arruamento-lazer-areas-verdes\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.valorPerdasArruamento ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-perdas-arruamento-lazer-areas-verdes\' | translate}}\" ui-number-mask=2 min=0.00 max=1.00 ng-blur=calcularValorTotalGleba()></md-input-container></div></div><div layout=column flex><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label>{{\'label-valor-unitario\' | translate}}:*</span></div></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=avaliacao.tipoValorUnitario name=\"{{nomeCampo}} - {{\'label-valor-unitario\' | translate}}\" ng-change=changeTipoValorUnitario()><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaValorUnitario\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div></div><div ng-if=\"avaliacao.tipoValorUnitario == \'INFERENCIA_ESTATISTICA\'\" layout=column flex><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-lote-paradigma\'| translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"\" class=barraRolagemTextArea ng-model=avaliacao.loteParadigma ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-lote-paradigma\' | translate}}\"></textarea></md-input-container></div><v-pane expanded=true><v-pane-header class=\"textoAccordionPvgLaudo teste\">{{\'title-variaveis-amostra\' | translate}}</v-pane-header><v-pane-content><div layout=column flex><variavel-amostra avaliacao=avaliacao variaveis=avaliacao.variaveis amostras=avaliacao.amostras variaveis-amostras=avaliacao.variaveisAmostras redirecionar-selecionar-amostras=redirecionarSelecionarAmostras redirecionar-adicionar-variavel-amostras=redirecionarAdicionarVariavelAmostras detalhar-laudo-relatorio=detalharLaudoRelatorio salvar-como-rascunho-laudo-relatorio=salvarComoRascunhoLaudoRelatorio tipo-laudo=identificacao.tipo is-benfeitoria-edificacao=isBenfeitoriaEdificacao is-benfeitoria-sem-edificacao=isBenfeitoriaSemEdificacao rip=identificacao.rip is-unidade-autonoma=isUnidadeAutonoma></variavel-amostra></div></v-pane-content></v-pane><div class=m-t-20 layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'label-valor-unitario-adotado\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.valorUnitarioAdotado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-valor-unitario-adotado\' | translate}}\" ui-number-mask=2 ng-blur=calcularValorTotalGleba()></md-input-container><label ng-bind=\"(avaliacao.valorUnitarioAdotado > 0 ? convertNumeroPorExtenso(avaliacao.valorUnitarioAdotado) : \'\')\"></label></div><div layout=column class=m-t-20 flex><documento-laudo-relatorio-padrao hint=\"\'Devem ser anexados os resultados do modelo estatístico\'\" titulo=\"\'Relatórios Estatísticos\'\" detalhar-laudo-relatorio=detalharLaudoRelatorio lista-documentos-laudo=avaliacao.resultadosAvaliacao.relatoriosEstatisticos campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_02 tipo-anexo=\"\'Relatórios Estatísticos\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=avaliacao.resultadosAvaliacao.isArquivoModelo ng-change=limparDadosArquivosModelo(); name=\"{{\'label-incluir-arquivo-modelo\' | translate}}\" ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-incluir-arquivo-modelo\' | translate}}</label><md-tooltip md-direction=top>Adicionar arquivo do software utilizado para o cálculo</md-tooltip></md-checkbox></div><div layout=column flex ng-if=avaliacao.resultadosAvaliacao.isArquivoModelo><documento-laudo-relatorio-padrao hint=\"\'Adicionar arquivo do software utilizado para o cálculo\'\" titulo=\"\'Arquivos do Modelo\'\" lista-documentos-laudo=avaliacao.resultadosAvaliacao.arquivosModelo campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_03\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-relatorio-padrao></div></div><div ng-if=\"avaliacao.tipoValorUnitario == \'DOMINIO_PLENO\'\" layout=column flex><div layout=row layout-xs=column class=m-t-10><label flex=100><span class=datepicker-label><b>{{\'label-fonte\' |translate}}:</b></span> <span class=datepicker-label ng-bind=avaliacao.dominioPlenoFonte></span></label></div><div layout=row layout-xs=column class=m-t-10><label flex=100><span class=datepicker-label><b>{{\'label-valor-m\' |translate}}:</b></span> <span class=datepicker-label ng-bind=\"(avaliacao.valorUnitarioAdotado | currency).replace(\'R$\', \'\')\"></span></label></div><div layout=row layout-xs=column class=\"m-t-10 m-b-2\"><label flex=100><span class=datepicker-label><b>{{\'label-data\' |translate}}:</b></span> <span class=datepicker-label ng-bind=\"(avaliacao.dataDominioPleno | date : \'dd/MM/yyyy\')\"></span></label></div></div><div ng-if=\"avaliacao.tipoValorUnitario == \'OUTRA_METODOLOGIA\'\" layout=column flex><div class=m-t-20 layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'label-valor-m\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio ng-model=avaliacao.valorUnitarioAdotado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-valor-m\' | translate}}\" ui-number-mask=2 ng-blur=calcularValorTotalGleba()></md-input-container><label ng-bind=\"(avaliacao.valorUnitarioAdotado > 0 ? convertNumeroPorExtenso(avaliacao.valorUnitarioAdotado) : \'\')\"></label></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-descricao-metodologia\'| translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio class=barraRolagemTextArea ng-model=avaliacao.descricaoMetodologia ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-metodologia\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=33 style=\"margin-top: -15px\"><span class=datepicker-label>{{\'label-data\' | translate}}:</span></div></div><div layout=row layout-xs=column flex=100 flex-xs=100><div flex=33 layout=column><md-input class=md-block><md-datepicker ng-disabled=detalharLaudoRelatorio md-max-date=dataAtual flex-xs=100 flex-sm flex-md ng-model=avaliacao.dataOutraMetodologia md-placeholder=\"{{\'label-data\' | translate}}\"></md-datepicker></md-input></div></div><div layout=column flex><documento-laudo-relatorio-padrao detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=avaliacao.outraMetodologiaAnexos campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_02 titulo=\"\'Anexos\'\" tipo-anexo=\"\'Avaliação - Anexos\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div></div><div layout=column flex><div class=m-t-20><div layout=row layout-xs=row flex=100 flex-xs flex-sm ng-if=\"avaliacao.modeloAvaliacaoGleba == \'OSCAR_OLAVE\'\"><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'label-lucro-empreendedor\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio ng-required=\"avaliacao.modeloAvaliacaoGleba == \'OSCAR_OLAVE\'\" ng-model=avaliacao.lucroEmpregador ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-lucro-empreendedor\' | translate}}\" ui-number-mask=2 min=0 max=999.99 ng-blur=calcularValorTotalGleba()></md-input-container><div flex=50 class=\"md-block m-t-5\"><label ng-bind=\"\'%\'\"></label></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label ng-if=\"avaliacao.modeloAvaliacaoGleba != \'JOSE_BARBAGELATA\'\">{{\'label-despesas-com-urbanizacao\' | translate}}:*</label><label ng-if=\"avaliacao.modeloAvaliacaoGleba == \'JOSE_BARBAGELATA\'\">{{\'label-despesas-unitaria-com-urbanizacao\' | translate}}:*</label><input ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.despesasUrbanizacao ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-despesas-com-urbanizacao\' | translate}}\" ui-number-mask=2 ng-blur=calcularValorTotalGleba()></md-input-container><label ng-bind=\"(avaliacao.despesasUrbanizacao > 0 ? convertNumeroPorExtenso(avaliacao.despesasUrbanizacao) : \'\')\"></label></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-fonte\'| translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"\" class=barraRolagemTextArea ng-model=avaliacao.descricaoFonte ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-fonte\' | translate}}\"></textarea></md-input-container></div><div layout=column flex><documento-laudo-relatorio-padrao detalhar-laudo-avaliacao=detalharLaudoRelatorio lista-documentos-laudo=avaliacao.anexos campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_02 titulo=\"\'Anexos\'\" tipo-anexo=\"\'Avaliação - Anexos\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div></div></fieldset><fieldset class=fieldsetArredondado layout=column layout-xs=column><div layout=row layout-xs=column class=m-t-20><h3 class=subtitulo-pagina>{{\'title-resultado-avaliacao\' | translate}}</h3></div><div class=m-b-30><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=md-block flex-gt-xs flex=100><span class=datepicker-label><b>{{\'label-valor-total-gleba\' | translate}}:</b></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><label>{{(avaliacao.valorTotalGleba | currency)}}</label></div><div flex=70 class=md-block><label ng-bind=\"(avaliacao.valorTotalGleba > 0 ? convertNumeroPorExtenso(avaliacao.valorTotalGleba) : \'\')\"></label></div></div></div></fieldset><div layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-encerramento\'| translate}}</label><textarea ng-disabled=detalharLaudoRelatorio class=barraRolagemTextArea ng-model=avaliacao.encerramento ui-mask-placeholder-char=space> </textarea></md-input-container></div></div></v-pane-content></v-pane></v-accordion></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/benfeitorias.tmpl.html","<ng-form name=formularioValidacao><md-content flex><div><v-accordion multiple class=vAccordion--default ng-repeat=\"benfeitoriaAvaliacao in listaBenfeitoriaAvaliacao\"><v-pane expanded=true><v-pane-header><label ng-bind=\"(\'title-benfeitoria\' | translate) + \' \'\n                            + benfeitoriaAvaliacao.benfeitoriaImovel.codigo + \' - \'\n                            + benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.descricao + \'/\'\n                            + (benfeitoriaAvaliacao.benfeitoriaImovel.edificacao ? benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoriaAvaliacao.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></v-pane-header><v-pane-content><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-tipo-acessao-benfeitoria\' | translate}}</label><md-select ng-disabled=detalharLaudoRelatorio ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria disabled><md-option ng-repeat=\"tipoBenfeitoria in listaTipoBenfeitoria\" ng-value=tipoBenfeitoria ng-selected=\"benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.id==tipoBenfeitoria.id\">{{tipoBenfeitoria.descricao}}</md-option></md-select></md-input-container><div layout=column flex=33 flex-xs=100 flex-sm=100 ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.id==1\"><md-input-container class=md-block><label style=\"position: inherit;  font-size: 10px\">{{\'label-tipo-especializacao-edificacao\' | translate}}</label><md-select ng-disabled=detalharLaudoRelatorio ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao ng-disabled=benfeitoriaAvaliacao.benfeitoriaImovel.isTipoEspecializacaoEdificacao><md-option ng-repeat=\"tipoEdificacao in listaTipoEdificacao\" ng-value=tipoEdificacao ng-selected=\"benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.id==tipoEdificacao.id\">{{tipoEdificacao.descricao}}</md-option></md-select></md-input-container><manter-edicao-campo is-campo=benfeitoriaAvaliacao.benfeitoriaImovel.isTipoEspecializacaoEdificacao valor=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao ng-show=\"!benfeitoriaAvaliacao.benfeitoriaImovel.isTipoEspecializacaoEdificacao && !detalharLaudoRelatorio\"></manter-edicao-campo></div><habilitar-edicao-campo style=\"margin-top: 20px\" is-campo=benfeitoriaAvaliacao.benfeitoriaImovel.isTipoEspecializacaoEdificacao ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.isTipoEspecializacaoEdificacao && benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.id==1 && !detalharLaudoRelatorio\"></habilitar-edicao-campo><div layout=column flex=33 flex-xs=100 flex-sm=100 ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.id==2\"><md-input-container class=md-block><label>{{\'label-tipo-especializacao-complementar\' | translate}}</label><md-select ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar ng-disabled=\"benfeitoriaAvaliacao.benfeitoriaImovel.isTipoEspecializacaoComplementar || detalharLaudoRelatorio\"><md-option ng-repeat=\"tipoComplementar in listaTipoComplementar\" ng-value=tipoComplementar ng-selected=\"benfeitoriaAvaliacao.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar.id==tipoComplementar.id\">{{tipoComplementar.descricao}}</md-option></md-select></md-input-container><manter-edicao-campo is-campo=benfeitoriaAvaliacao.benfeitoriaImovel.isTipoEspecializacaoComplementar valor=benfeitoriaAvaliacao.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar ng-show=\"!benfeitoriaAvaliacao.benfeitoriaImovel.isTipoEspecializacaoComplementar && !detalharLaudoRelatorio\"></manter-edicao-campo></div><habilitar-edicao-campo style=\"margin-top: 20px; padding-left: 0px\" is-campo=benfeitoriaAvaliacao.benfeitoriaImovel.isTipoEspecializacaoComplementar ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.isTipoEspecializacaoComplementar && benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.id==2 && !detalharLaudoRelatorio\"></habilitar-edicao-campo><div layout=column flex=33 flex-xs=100 flex-sm=100 ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.id==1\"><md-input-container class=md-block><label style=\"position: inherit;  font-size: 10px\">{{\'label-tipo-projeto\' | translate}}</label><md-select ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoProjeto ng-disabled=\"isTipoProjeto || detalharLaudoRelatorio\"><md-option ng-repeat=\"tipoProjeto in listaTiposProjeto\" ng-value=tipoProjeto ng-selected=\"benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoProjeto.id==tipoProjeto.id\">{{tipoProjeto.descricao}}</md-option></md-select></md-input-container><manter-edicao-campo is-campo=isTipoProjeto valor=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.tipoProjeto ng-show=\"!isTipoProjeto && !detalharLaudoRelatorio\"></manter-edicao-campo></div><habilitar-edicao-campo style=\"margin-top: 20px\" is-campo=isTipoProjeto ng-show=\"isTipoProjeto && benfeitoriaAvaliacao.benfeitoriaImovel.tipoBenfeitoria.id==1 && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=\"input-table md-block\" flex=100 flex-xs=100 flex-sm=100><label>{{\'label-descricao\' | translate}}</label><input ng-disabled=true ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.descricao name=\"{{\'label-descricao\' | translate}}\" disabled></md-input-container></div><div layout=row layout-xs=column><div layout=column flex=50 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-area-contruida\' | translate}}</label><input ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.areaConstruida name=\"{{\'label-area-contruida\' | translate}}\" ui-number-mask=2 ng-disabled=\"benfeitoriaAvaliacao.benfeitoriaImovel.isAreaConstruida || detalharLaudoRelatorio\"></md-input-container><manter-edicao-campo is-campo=benfeitoriaAvaliacao.benfeitoriaImovel.isAreaConstruida valor=benfeitoriaAvaliacao.benfeitoriaImovel.areaConstruida ng-show=\"!benfeitoriaAvaliacao.benfeitoriaImovel.isAreaConstruida && !detalharLaudoRelatorio\"></manter-edicao-campo></div><habilitar-edicao-campo is-campo=benfeitoriaAvaliacao.benfeitoriaImovel.isAreaConstruida ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.isAreaConstruida && !detalharLaudoRelatorio\"></habilitar-edicao-campo><div layout=column flex=50 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-tipo-area-projecao\' | translate}}</label><input ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.areaProjecao name=\"{{\'label-tipo-area-projecao\' | translate}}\" ui-number-mask=2 ng-disabled=\"benfeitoriaAvaliacao.benfeitoriaImovel.isAreaProjecao || detalharLaudoRelatorio\"></md-input-container><manter-edicao-campo is-campo=benfeitoriaAvaliacao.benfeitoriaImovel.isAreaProjecao valor=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.areaProjecao ng-show=\"!benfeitoriaAvaliacao.benfeitoriaImovel.isAreaProjecao && !detalharLaudoRelatorio\"></manter-edicao-campo></div><habilitar-edicao-campo is-campo=benfeitoriaAvaliacao.benfeitoriaImovel.isAreaProjecao ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.isAreaProjecao && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-taxa-ocupacao\' | translate}}</label><input ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.taxaOcupacao name=\"{{\'label-taxa-ocupacao\' | translate}}\" ui-number-mask=2 ng-disabled=true></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-indice-aproveitamento\' | translate}}</label><input ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.indiceAproveitamento name=\"{{\'label-indice-aproveitamento\' | translate}}\" ui-number-mask=2 ng-disabled=true></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-numero-pavimento\' | translate}}</label><input ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.numeroPavimentos name=\"{{\'label-numero-pavimento\' | translate}}\" type=number disabled></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-vaga-garagem-coberta\' | translate}}</label><input ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.numeroVagasCobertas name=\"{{\'label-vaga-garagem-coberta\' | translate}}\" type=number disabled></md-input-container><div layout=column flex=33 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-vaga-garagem-descoberta\' | translate}}</label><input ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.numeroVagasDescobertas name=\"{{\'label-vaga-garagem-descoberta\' | translate}}\" type=number ng-disabled=\"benfeitoriaAvaliacao.benfeitoriaImovel.isVagasGaragemDescobertas || detalharLaudoRelatorio\"></md-input-container><manter-edicao-campo is-campo=benfeitoriaAvaliacao.benfeitoriaImovel.isVagasGaragemDescobertas valor=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.numeroVagasDescobertas ng-show=\"!benfeitoriaAvaliacao.benfeitoriaImovel.isVagasGaragemDescobertas && !detalharLaudoRelatorio\"></manter-edicao-campo></div><habilitar-edicao-campo is-campo=benfeitoriaAvaliacao.benfeitoriaImovel.isVagasGaragemDescobertas ng-show=\"benfeitoriaAvaliacao.benfeitoriaImovel.isVagasGaragemDescobertas && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row layout-xs=column flex-sm=column><div class=md-block flex=33 flex-xs=100 flex-sm=100><md-checkbox class=md-primary ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.isPossuiElevador name=\"{{\'label-possui-elevador\' | translate}}\" disabled><label class=text-black>{{\'label-possui-elevador\' | translate}}</label></md-checkbox></div></div><div layout=row layout-xs=column flex-sm=column><div class=md-block flex=50 flex-xs=100 flex-sm=100><md-checkbox class=md-primary ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.isPossuiEtiquetaConservacao name=\"{{\'label-possui-etiqueta-conservacao-energia\' | translate}}\" disabled><label class=text-black>{{\'label-possui-etiqueta-conservacao-energia\' | translate}}</label></md-checkbox></div></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-tipo\' | translate}}</label><md-select ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.tipo disabled><md-option ng-repeat=\"tipo in listaTiposImovel\" ng-value=tipo ng-selected=\"benfeitoriaAvaliacao.benfeitoriaImovel.tipo.id == tipo.id\">{{tipo.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-tipo-parcial\' | translate}}</label><md-select ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.tipoParcial disabled><md-option ng-repeat=\"tipoParcial in listaTiposImovel\" ng-value=tipoParcial ng-selected=\"benfeitoriaAvaliacao.benfeitoriaImovel.tipoParcial.id == tipoParcial.id\">{{tipoParcial.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-nivel\' | translate}}</label><md-select ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.nivel disabled><md-option ng-repeat=\"nivel in listaNaturezasImovel\" ng-value=nivel ng-selected=\"benfeitoriaAvaliacao.benfeitoriaImovel.nivel.id == nivel.id\">{{nivel.descricao}}</md-option></md-select></md-input-container></div><documento-edificacao-benfeitoria detalhar-laudo-relatorio=detalharLaudoRelatorio lista-documentos-edificacao-benfeitoria=benfeitoriaAvaliacao.benfeitoriaImovel.edificacao.documentacao lista-benfeitoria-avaliacao=listaBenfeitoriaAvaliacao benfeitorias=benfeitorias redirecionar-cadastrar-documento-edificacao-benfeitoria=redirecionarCadastrarDocumentoEdificacaoBenfeitoria titulo=\"\'Documentação\'\" custom-nome-label=\"\'Nome do Documento\'\" custom-button-label=\"\'Selecionar\'\" campo-data=\"\'true\'\"></documento-edificacao-benfeitoria><div layout=row flex><h3>{{\'title-condicoes-acessibilidade\' | translate}}</h3></div><div layout=row layout-xs=column class=m-t-20><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><div style=\"padding-left: 3px\"><label class=new-label-large>{{\'label-vagas-deficientes-acessiveis\' | translate}}</label></div><md-select ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.isVagaDeficienteAcessivel ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"item in condicoes\" ng-value=item.valor ng-selected=\"item.valor == benfeitoriaAvaliacao.benfeitoriaImovel.isVagaDeficienteAcessivel\">{{item.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><div style=\"padding-left: 3px\"><label class=new-label-large>{{\'label-acessos-interior-acessiveis\' | translate}}</label></div><md-select ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.isAcessosInteriorAcessivel ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"item in condicoes\" ng-value=item.valor ng-selected=\"item.valor == benfeitoriaAvaliacao.benfeitoriaImovel.isAcessosInteriorAcessivel\">{{item.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><div style=\"padding-left: 3px\"><label class=new-label-large>{{\'label-banheiro-acessivel\' | translate}}</label></div><md-select ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.isBanheiroAcessivel ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"item in condicoes\" ng-value=item.valor ng-selected=\"item.valor == benfeitoriaAvaliacao.benfeitoriaImovel.isBanheiroAcessivel\">{{item.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><div style=\"padding-left: 3px\"><label class=new-label-large>{{\'label-elevador-acessivel\' | translate}}</label></div><md-select ng-model=benfeitoriaAvaliacao.benfeitoriaImovel.isElevadorAcessivel ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"item in condicoes\" ng-value=item.valor ng-selected=\"item.valor == benfeitoriaAvaliacao.benfeitoriaImovel.isElevadorAcessivel\">{{item.nome}}</md-option></md-select></md-input-container></div><documento-laudo-relatorio-padrao lista-documentos-laudo=benfeitoriaAvaliacao.benfeitoriaImovel.arquivosPlanta titulo=\"\'Arquivos de Planta\'\" campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_01 detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-relatorio-padrao><div layout=row flex><h3>{{\'title-descricao-benfeitoria\' | translate}}</h3></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-divisao-interna\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=benfeitoriaAvaliacao.divisaoInterna name=\"{{\'label-divisao-interna\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-material-construtivo-interno\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=benfeitoriaAvaliacao.materiaisConstrutivosInternos name=\"{{\'label-material-construtivo-interno\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-estrutura\' | translate}}<md-tooltip md-direction=top>estrutura convencional de concreto armado, estrutura de concreto protendido, estrutura metálica, estrutura de madeira, alvenaria estrutural, mista etc.</md-tooltip></label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=benfeitoriaAvaliacao.estrutura name=\"{{\'label-estrutura\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-cobertura\' | translate}}<md-tooltip md-direction=top>laje plana, telhas cerâmicas, telhas metálicas, telhas de fibrocimento etc.</md-tooltip></label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=benfeitoriaAvaliacao.cobertura name=\"{{\'label-cobertura\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-fachada\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=benfeitoriaAvaliacao.fachada name=\"{{\'label-fachada\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-instalação-equipamento\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=benfeitoriaAvaliacao.instalacoesEquipamentos name=\"{{\'label-instalação-equipamento\' | translate}}\"></textarea></md-input-container></div></v-pane-content></v-pane></v-accordion></div></md-content></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/cancelamentoLaudo.tmpl.html","<ng-form name=cancelamentoLaudo><label class=\"sidenavTitulos deslocamentoTituloData\">{{\'tr-data\' | translate}}:</label><div flex layout=row><!--<md-datepicker flex=\"30\"--><!--md-placeholder=\"{{\'tr-data\' | translate}}\" style=\"margin-top: 15px;\"--><!--ng-model=\"\"></md-datepicker>--><md-input-container flex=30><label class=sidenavTitulos>{{\'label-responsavel\' | translate}}</label><input name=\"{{\'label-responsavel\' | translate}}\"></md-input-container></div><div layout=column><md-input-container class=\"md-block espacamentoInputs\" flex flex-xs flex-sm><label class=sidenavTitulos>{{\'title-motivo\' | translate}}:</label><md-select ng-model=\"\" name=\"{{nomeCampo}} - {{\'title-moeda\' | translate}}\" placeholder=\"{{\'title-motivo\' | translate}}\"><md-option ng-repeat=\"\" ng-value=\"\" ng-selected=\"\">{{}}</md-option></md-select></md-input-container><md-input-container flex=30><label class=sidenavTitulos>{{\'label-justificativa\' | translate}}</label><input name=\"{{\'label-justificativa\' | translate}}\"></md-input-container></div></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/detalharRevalidacao.html","<div style=\"margin-bottom: 10px\"><div layout=row layout-xs=column ng-if=\"revalidacoes.length > 0\"><div flex><h3 class=subtitulo-pagina-detalhar>{{\'title-dados-revalidacao\' | translate}}</h3></div></div><v-accordion multiple class=vAccordion--default><v-pane ng-repeat=\"item in revalidacoes\" ng-key=item.id><v-pane-header style=\"margin-top:10px !important; margin-bottom: 5px\"><div layout=row layout-xs=column class=m-b-20><md-input-container class=md-block flex=4><div><md-icon ng-bind=\"\'menu\'\"></md-icon></div></md-input-container><md-input-container ng-if=situacoesPendentes(item.situacao) flex=4><b style=\"color: #d41515 !important\">{{listaSituacoes[item.situacao]}}</b></md-input-container><md-input-container class=md-block flex=20 ng-if=!situacoesPendentes(item.situacao)><div><b>{{\'title-data-revalidacao\' | translate}}</b> <span>{{ item.dataAssinatura | date: \'dd/MM/yyyy\' : \'-0300\' }}</span></div></md-input-container></div></v-pane-header><v-pane-content><div layout=row layout-xs=column class=\"m-b-20 m-t-20\"><span class=negritoTexto>Extensão da Validade - a partir da Data Final de Validade: </span>{{item.mesesRevalidacao}} Meses</div><div><responsavel-tecnico incluir-usuario-logado=false lista-responsaveis-tecnicos=item.responsaveis assinatura-nota-tecnica=false assinatura-laudo-relatorio=false detalhar-laudo-relatorio=true descricao-hint=\"\'Autor do Relatório de Valor de Referência\'\" modo=compacto></responsavel-tecnico></div><fieldset class=fieldsetArredondado layout=column layout-xs=column><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar>{{\'title-arquivos\' | translate}}</h3></div></div><div class=m-b-20 flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th class=th-margin-pvgs md-column>{{\'label-nome-arquivo\' | translate}}</th><th ng-show=\"item.revalidacaoLaudoRelatorio != \'PREENCHER_SISTEMA\'\" class=th-margin-pvgs md-column>{{\'label-arquivo\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-if=\"item.revalidacaoLaudoRelatorio == \'PREENCHER_SISTEMA\'\"><td md-cell><a ng-if=verificarExibicaoDownloadLaudoRevalidacao(vm.laudoAvaliacao.situacaoLaudo) class=mouse-pointer ng-href={{downloadRevalidacaoLaudoAvaliacao()}} target=_blank>{{\'title-nota-revalidacao\' | translate}}</a> <a ng-if=verificarExibicaoDocumentoRevalidacaoLaudo(vm.laudoAvaliacao.situacaoLaudo) class=mouse-pointer href=\"\" ng-click=visualizarDocumentoRevalidacaoLaudo() target=_blank>{{\'title-nota-revalidacao\' | translate}}</a></td></tr><tr md-row ng-repeat=\"anexos in item.arquivosLaudoRevalidacao\" ng-show=\"item.revalidacaoLaudoRelatorio != \'PREENCHER_SISTEMA\'\"><td md-cell><label ng-bind=anexos.nome></label></td><td md-cell><a ng-if=\"anexos.arquivo != null\" ng-href={{baixarArquivo(anexos.arquivo.id)}} target=_blank><label ng-bind=anexos.arquivo.nome></label></a><a ng-if=\"anexos.arquivo == null\" target=_blank><label ng-bind=anexos.nome></label></a></td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=item.revalidacaoDocumentoSei.isEnviarDocumento ng-disabled=true><label class=text-black>{{\'label-enviar-documento-para-sei\' | translate}}</label></md-checkbox></div><div ng-if=item.revalidacaoDocumentoSei.isEnviarDocumento layout=column flex><md-radio-group flex class=\"m-l-30 tamanho-radio-button-avaliacao md-block\" ng-model=item.revalidacaoDocumentoSei.tipoEnviarDocumento ng-disabled=true ng-required=item.revalidacaoDocumentoSei.isEnviarDocumento><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'CRIAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-criar-novo-processo-sei\' | translate}}</div></md-radio-button></div><div class=\"m-b-10 m-l-60\" layout=row ng-if=\"item.revalidacaoDocumentoSei.tipoEnviarDocumento == \'CRIAR_PROCESSO_SEI\' && item.revalidacaoDocumentoSei.numeroProcessoSei\"><div class=texto-preto>{{\'label-n-processo\' | translate}}{{\': \' + laudoAvaliacao.revalidacaoLaudoAvaliacao.revalidacaoDocumentoSei.numeroProcessoSei | processoSei}}</div></div><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'UTILIZAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-utilizar-processo-existente-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><md-input-container ng-if=\"item.revalidacaoDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" class=\"md-block m-l-60\" flex=30 flex-xs=100 flex-sm=100><label>{{\'label-numero-processo-sei\' | translate}}</label><input type=text ui-mask=99999.999999/9999-99 ng-required=\"item.revalidacaoDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" ng-model=item.revalidacaoDocumentoSei.numeroProcessoSei ng-disabled=true></md-input-container></fieldset><fieldset class=fieldsetArredondado layout=column layout-xs=column><div layout=row layout-xs=column><div flex><h3 class=subtitulo-pagina-detalhar>{{\'title-anexos\' | translate}}</h3></div></div><div class=m-b-20 flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th class=th-margin-pvgs md-column>{{\'label-nome-arquivo\' | translate}}</th><th ng-show=\"item.revalidacaoLaudoRelatorio != \'PREENCHER_SISTEMA\'\" class=th-margin-pvgs md-column>{{\'label-arquivo\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-if=\"item.revalidacaoLaudoRelatorio == \'PREENCHER_SISTEMA\'\"><td md-cell><a ng-if=verificarExibicaoDownloadLaudoRevalidacao(vm.laudoAvaliacao.situacaoLaudo) class=mouse-pointer ng-href={{downloadRevalidacaoLaudoAvaliacao()}} target=_blank>Nota de Revalidação</a> <a ng-if=verificarExibicaoDocumentoRevalidacaoLaudo(vm.laudoAvaliacao.situacaoLaudo) class=mouse-pointer href=\"\" ng-click=visualizarDocumentoRevalidacaoLaudo() target=_blank>Nota de Revalidação</a></td></tr><tr md-row ng-repeat=\"anexos in item.anexos\" ng-show=\"item.revalidacaoLaudoRelatorio != \'PREENCHER_SISTEMA\'\"><td md-cell><label ng-bind=anexos.nome></label></td><td md-cell><a ng-if=\"anexos.arquivo != null\" ng-href={{baixarArquivo(anexos.arquivo.id)}} target=_blank><label ng-bind=anexos.arquivo.nome></label></a><a ng-if=\"anexos.arquivo == null\" target=_blank><label ng-bind=anexos.nome></label></a></td></tr></tbody></table></md-table-container></md-content></div></div></fieldset><div class=m-b-20 flex ng-show=\"item.revalidacaoLaudoRelatorio == \'UPLOAD_ARQUIVO\'\"><div class=m-b-20 layout=row flex=100><div flex=100 layout=column><md-checkbox class=md-primary ng-disabled=true ng-model=item.termoResponsabilidade.isAssinado name=\"{{\'label-termo-responsabilidade\' | translate}}\"><label class=text-black>{{\'Eu, \' + laudoAvaliacao.revalidacaoLaudoAvaliacao.termoResponsabilidade.nomeAssinador + \', CPF: \' + (laudoAvaliacao.revalidacaoLaudoAvaliacao.termoResponsabilidade.cpfAssinador | cpfcnpj) + \', \'}}{{\'mensagem-termo-responsabilidade-relatorio-valor-referencia\' | translate}}</label></md-checkbox></div></div></div></v-pane-content></v-pane></v-accordion></div>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/detalhesImovel.tmpl.html","<ng-form name=formularioValidacao><md-content flex><div><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header class=textoAccordionPvgLaudo>{{\'title-localizacao-imovel\' | translate}}</v-pane-header><v-pane-content style=\"position: inherit;  font-size: 10px\"><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-pais\' | translate}}</label><input ng-model=dadosEnderecamento.pais disabled></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 ng-show=\"!dadosEnderecamento.pais || dadosEnderecamento.pais==BRASIL\"><label style=\"position: inherit;  font-size: 10px\">{{\'label-cep\' | translate}}</label><input ng-model=dadosEnderecamento.cep disabled ui-br-cep-mask></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100 ng-show=\"!dadosEnderecamento.pais || dadosEnderecamento.pais==BRASIL\"><label style=\"position: inherit;  font-size: 10px\">{{\'label-uf\' | translate}}</label><input ng-model=dadosEnderecamento.uf disabled></md-input-container></div><div layout=row layout-xs=column flex-sm=column ng-show=\"!dadosEnderecamento.pais || dadosEnderecamento.pais==BRASIL\"><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-municipio\' | translate}}</label><input ng-model=dadosEnderecamento.municipio disabled></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tipo-logradouro\' | translate}}</label><input ng-model=dadosEnderecamento.tipoLogradouro disabled></md-input-container></div><div layout=row layout-xs=column flex-sm=column ng-show=\"!dadosEnderecamento.pais || dadosEnderecamento.pais==BRASIL\"><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-logradouro\' | translate}}</label><input ng-model=dadosEnderecamento.logradouro disabled></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-numero\' | translate}}</label><input ng-model=dadosEnderecamento.numero disabled></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-bairro-distrito\' | translate}}</label><input ng-model=dadosEnderecamento.bairro disabled></md-input-container></div><div layout=row layout-xs=column flex-sm=column ng-show=\"!dadosEnderecamento.pais || dadosEnderecamento.pais==BRASIL\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-complemento\' | translate}}</label><input ng-model=dadosEnderecamento.complemento disabled></md-input-container></div><div layout=row layout-xs=column flex-sm=column ng-show=\"dadosEnderecamento.pais && dadosEnderecamento.pais!=BRASIL\"><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-cidade-exterior\' | translate}}</label><input ng-model=dadosEnderecamento.cidadeExterior disabled></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-codigo-postal\' | translate}}</label><input ng-model=dadosEnderecamento.codigoPostal disabled></md-input-container></div><div layout=row layout-xs=column flex-sm=column ng-show=\"dadosEnderecamento.pais && dadosEnderecamento.pais!=BRASIL\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-endereco\' | translate}}</label><input ng-model=dadosEnderecamento.endereco disabled></md-input-container></div></v-pane-content></v-pane></v-accordion></div><div><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header class=textoAccordionPvgLaudo>{{\'title-detalhes-imovel\' | translate}}</v-pane-header><v-pane-content><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-tipo-imovel\' | translate}}</label><md-select ng-model=imovel.tipoImovel disabled><md-option ng-repeat=\"tipoImovel in listaTiposImovel\" ng-value=tipoImovel ng-selected=\"imovel.tipoImovel.id == tipoImovel.id\">{{tipoImovel.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-natureza-imovel\' | translate}}</label><md-select ng-model=imovel.naturezaImovel disabled><md-option ng-repeat=\"naturezaImovel in listaNaturezasImovel\" ng-value=naturezaImovel ng-selected=\"imovel.naturezaImovel.id == naturezaImovel.id\">{{naturezaImovel.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-n-inscricao\' | translate}}{{imovel.naturezaImovel.descricao == \'Urbano\' ? \' Municipal\' : \' \' + imovel.naturezaImovel.descricao}}</label><input ng-model=imovel.numeroInscricao ng-disabled=true name=\"{{\'label-n-inscricao\' | translate}}\"></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=\"input-table md-block\" flex=100 flex-xs=100 flex-sm=100><label>{{\'label-observacao-natureza-imovel\' | translate}}</label><input ng-disabled=detalharLaudoRelatorio ng-model=detalhesImovel.observacoesNaturezaImovel name=\"{{\'label-observacao-natureza-imovel\' | translate}}\"></md-input-container></div><div layout=row layout-xs=column flex-sm=column><div layout=column flex=95 flex-xs=95 flex-sm=95><md-input-container class=\"input-table md-block\"><label>{{\'label-observacao-geral\' | translate}}</label><input ng-model=imovel.observacoesGerais ng-disabled=\"isObservacoesGerais || detalharLaudoRelatorio\" name=\"{{\'label-observacao-geral\' | translate}}\"></md-input-container></div><manter-edicao-campo is-campo=isObservacoesGerais valor=imovel.observacoesGerais ng-show=\"!isObservacoesGerais && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isObservacoesGerais ng-show=\"isObservacoesGerais && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-n-processo\' | translate}}</label><input ng-model=imovel.numeroProcesso name=\"{{\'label-n-processo\' | translate}}\" disabled></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-forma-incorporacao\' | translate}}</label><md-select ng-model=imovel.formaIncorporacao disabled><md-option ng-repeat=\"formaIncorporacao in listaTiposImovel\" ng-value=formaIncorporacao ng-selected=\"imovel.formaIncorporacao.id == formaIncorporacao.id\">{{formaIncorporacao.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{}}</label><md-select ng-model=imovel.direitoAdquirido disabled><md-option ng-repeat=\"direitoAdquirido in listaNaturezasImovel\" ng-value=direitoAdquirido ng-selected=\"imovel.direitoAdquirido.id == direitoAdquirido.id\">{{direitoAdquirido.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex-sm=column><div class=\"md-block m-t-20\" flex=33 flex-xs=100 flex-sm=100><md-checkbox class=md-primary ng-model=imovel.isDoacaoEncargo name=\"{{\'label-doacao-com-encargo\' | translate}}\" disabled><label class=text-black>{{\'label-doacao-com-encargo\' | translate}}</label></md-checkbox></div><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-valor-encargo\' | translate}}</label><input ng-model=imovel.valorEncargo ui-number-mask=2 name=\"{{\'label-valor-encargo\' | translate}}\" disabled></md-input-container><md-input class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-data-final-encargo\' | translate}}</label><md-datepicker ng-model=imovel.dataFinalEncargo md-placeholder=\"{{\'label-data\' | translate}}\" disabled></md-datepicker></md-input></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=\"input-table md-block\" flex=100 flex-xs=100 flex-sm=100><label>{{\'label-outro-contrato-pessoal\' | translate}}</label><input ng-disabled=true ng-model=imovel.outroContratoPessoal name=\"{{\'label-outro-contrato-pessoal\' | translate}}\" disabled></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-fundamento\' | translate}}</label><md-select ng-model=imovel.fundamento disabled><md-option ng-repeat=\"fundamento in listaTiposImovel\" ng-value=formaIncorporacao ng-selected=\"imovel.fundamento.id == fundamento.id\">{{fundamento.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-situacao-incorporacao\' | translate}}</label><md-select ng-model=imovel.situacaoIncorporacao disabled><md-option ng-repeat=\"situacaoIncorporacao in listaTiposImovel\" ng-value=situacaoIncorporacao ng-selected=\"imovel.situacaoIncorporacao.id == situacaoIncorporacao.id\">{{situacaoIncorporacao.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label style=\"position: inherit;  font-size: 10px\">{{\'label-direito-adquirido\' | translate}}</label><md-select ng-model=imovel.formaIncorporacaoTipo disabled><md-option ng-repeat=\"formaIncorporacao in listaNaturezasImovel\" ng-value=formaIncorporacao ng-selected=\"imovel.formaIncorporacaoTipo.id == formaIncorporacao.id\">{{formaIncorporacao.descricao}}</md-option></md-select></md-input-container></div><div layout=column><matricula-transcricao detalhar-laudo-relatorio=detalharLaudoRelatorio lista-matriculas-transcricao=listaMatriculasTranscricao imovel=imovel></matricula-transcricao></div><br><div layout=column><documentos-cartoriais detalhar-laudo-relatorio=detalharLaudoRelatorio uf=dadosEnderecamento.uf lista-certidoes-cartoriais=listaCertidoesCartoriais anexo-laudo-relatorio=anexoLaudoRelatorio></documentos-cartoriais></div><div class=m-t-20 layout=row flex><h3>{{\'title-descricao-regiao\' | translate}}</h3></div><div layout=row layout-xs=column flex-sm=column><md-container class=md-block flex=100 flex-xs=100 flex-sm=100><legend>{{\'label-infraestrutura-urbana\' | translate}}</legend><br><div layout=row flex=100><div flex=30 layout=column flex-offset=5><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isPavimentacao name=\"{{\'label-pavimentacao\' | translate}}\"><label class=text-black>{{\'label-pavimentacao\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isIluminacaoPublica name=\"{{\'label-iluminacao-publica\' | translate}}\"><label class=text-black>{{\'label-iluminacao-publica\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isSarjetaGuia name=\"{{\'label-sarjeta-guia\' | translate}}\"><label class=text-black>{{\'label-sarjeta-guia\' | translate}}</label></md-checkbox></div><div flex=30 layout=column><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isRedeAgua name=\"{{\'label-rede-agua\' | translate}}\"><label class=text-black>{{\'label-rede-agua\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isRedeEletrica name=\"{{\'label-rede-eletrica\' | translate}}\"><label class=text-black>{{\'label-rede-eletrica\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isPasseio name=\"{{\'label-passeio\' | translate}}\"><label class=text-black>{{\'label-passeio\' | translate}}</label></md-checkbox></div><div flex=30 layout=column><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isRedeEsgoto name=\"{{\'label-rede-esgoto\' | translate}}\"><label class=text-black>{{\'label-rede-esgoto\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isColetaLixo name=\"{{\'label-coleta-lixo\' | translate}}\"><label class=text-black>{{\'label-coleta-lixo\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isOutrosInfraestruturaUrbana name=\"{{\'label-outros\' | translate}}\"><label class=text-black>{{\'label-outros\' | translate}}</label></md-checkbox></div></div></md-container></div><div layout=row layout-xs=column flex-sm=column ng-show=\"detalhesImovel.isOutrosInfraestruturaUrbana == true\"><md-input-container layout-xs=row flex=100 flex-xs=100 flex-sm=100 class=md-block layout-xs=column layout-sm=column><label>{{\'label-descricao-outros-infraestrutura-urbana\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio name=\"{{\'label-descricao-outras-infraestrutura-urbana\' | translate}}\" ui-mask-placeholder-char=space ng-model=detalhesImovel.descricaoOutrosInfraestruturaUrbana ng-required=\"detalhesImovel.isOutrosInfraestruturaUrbana == true\"></textarea></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-input-container layout-xs=row flex=100 flex-xs=100 flex-sm=100 class=md-block layout-xs=column layout-sm=column><label>{{\'label-observacao-infraestrutura-urbana\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio name=\"{{\'label-observacao-infraestrutura-urbana\' | translate}}\" ui-mask-placeholder-char=space ng-model=detalhesImovel.observacoesInfraestruturaUrbana></textarea></md-input-container></div><div layout=row layout-xs=column flex-sm=column><md-container class=md-block flex=100 flex-xs=100 flex-sm=100><legend>{{\'label-servicos-comunitarios\' | translate}}</legend><br><div layout=row flex=100><div flex=30 layout=column flex-offset=5><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isEscola name=\"{{\'label-escola\' | translate}}\"><label class=text-black>{{\'label-escola\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isTransporteColetivo name=\"{{\'label-transporte-coletivo\' | translate}}\"><label class=text-black>{{\'label-transporte-coletivo\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isAreaLazer name=\"{{\'label-area-lazer\' | translate}}\"><label class=text-black>{{\'label-area-lazer\' | translate}}</label></md-checkbox></div><div flex=30 layout=column><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isPostoSaude name=\"{{\'label-posto-saude\' | translate}}\"><label class=text-black>{{\'label-posto-saude\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isComercio name=\"{{\'label-comercio\' | translate}}\"><label class=text-black>{{\'label-comercio\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isColetaLixoServicoComunitario name=\"{{\'label-coleta-lixo\' | translate}}\"><label class=text-black>{{\'label-coleta-lixo\' | translate}}</label></md-checkbox></div><div flex=30 layout=column><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isSeguranca name=\"{{\'label-seguranca\' | translate}}\"><label class=text-black>{{\'label-seguranca\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isRedeBancaria name=\"{{\'label-rede-bancaria\' | translate}}\"><label class=text-black>{{\'label-rede-bancaria\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=detalhesImovel.isOutrosServicosComunitarios name=\"{{\'label-outros\' | translate}}\"><label class=text-black>{{\'label-outros\' | translate}}</label></md-checkbox></div></div></md-container></div><div layout=row layout-xs=column flex-sm=column ng-show=\"detalhesImovel.isOutrosServicosComunitarios==true\"><md-input-container layout-xs=row flex=100 flex-xs=100 flex-sm=100 class=md-block layout-xs=column layout-sm=column><label>{{\'label-descricao-outros-servicos-comunitarios\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio name=\"{{\'label-descricao-outros-servicos-comunitarios\' | translate}}\" ui-mask-placeholder-char=space ng-model=detalhesImovel.descricaoOutrosServicosComunitarios ng-required=\"detalhesImovel.isOutrosServicosComunitarios==true\"></textarea></md-input-container></div></v-pane-content></v-pane></v-accordion></div><div><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header class=textoAccordionPvgLaudo>{{\'title-proprietario-imovel\' | translate}}</v-pane-header><v-pane-content style=\"position: inherit;  font-size: 10px\"><div layout=row><h4>{{\'title-proprietario\' | translate}}:</h4></div><div layout=row><md-input-container class=md-block flex=\"{{detalhesImovel.tipoProprietarioReal == AUTARQUIA_FUNDACAO_FEDERAL || detalhesImovel.tipoProprietarioReal == AUTARQUIA_FUNDACAO ? \'33\' : \'100\'}}\" flex-xs=100 flex-sm=100><label>{{\'label-tipo-proprietario\' | translate}}</label><md-select ng-model=detalhesImovel.tipoProprietarioReal disabled><md-option ng-repeat=\"tipoProprietario in listaTiposProprietarios\" ng-value=tipoProprietario.descricao ng-selected=\"tipoProprietario.id==dadosProprietario.tipoProprietarioReal.id\">{{tipoProprietario.descricao}}</md-option></md-select></md-input-container><md-input-container ng-show=\"detalhesImovel.tipoProprietarioReal == AUTARQUIA_FUNDACAO_FEDERAL || detalhesImovel.tipoProprietarioReal == AUTARQUIA_FUNDACAO\" class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-orgao-entidade\' | translate}}</label><input ng-model=dadosProprietario.orgaoEntidade disabled name=\"{{\'label-orgao-entidade\' | translate}}\"></md-input-container><md-input-container ng-show=\"detalhesImovel.tipoProprietarioReal == AUTARQUIA_FUNDACAO_FEDERAL || detalhesImovel.tipoProprietarioReal == AUTARQUIA_FUNDACAO\" class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-ug-especifica\' | translate}}</label><input ng-model=dadosProprietario.unidadeUsuario name=\"{{\'label-ug-especifica\' | translate}}\" disabled></md-input-container></div><div layout=row ng-if=\"detalhesImovel.tipoProprietarioReal!=TERCEIROS\"><md-container flex=50 class=\"md-block margin-container\"><md-radio-group id=radioIncra ng-model=dadosProprietario.isIntegralmente disabled class=\"th-espacamento-radio-button tamanho-radio-button-avaliacao\" layout=row><label>{{\'label-tipo-proprietario\' | translate}}:</label><br><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" ng-value=true><label class=texto-preto>{{\'label-integralmente\' | translate}}</label></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" ng-value=false><label class=texto-preto>{{\'label-parcialmente\' | translate}}</label></md-radio-button></md-radio-group></md-container></div><div layout=row ng-show=\"dadosProprietario.isIntegralmente==false && detalhesImovel.tipoProprietarioReal!=TERCEIROS\"><md-input-container class=md-block flex=\"{{dadosProprietario.tipoProprietarioParcial == AUTARQUIA_FUNDACAO_FEDERAL || dadosProprietario.tipoProprietarioParcial == AUTARQUIA_FUNDACAO ? \'33\' : \'100\'}}\" flex-xs=100 flex-xs=100 flex-sm=100 ng-show=\"dadosProprietario.isIntegralmente==false\"><label ng-bind=\"\'label-proprietario-parcial\' | translate\"></label><md-select ng-disabled=detalharLaudoRelatorio ng-model=dadosProprietario.tipoProprietarioParcial name=\"{{\'label-proprietario-parcial\' | translate}}\"><md-option ng-repeat=\"tipoProprietarioParcial in listaTiposProprietarioParcial\" ng-value=tipoProprietarioParcial ng-selected=\"tipoProprietarioParcial.id==dadosProprietario.tipoProprietarioParcial.id\"><label ng-bing=tipoProprietarioParcial.descricao></label></md-option></md-select></md-input-container><md-input-container class=md-block flex=33 ng-show=\"dadosProprietario.tipoProprietarioParcial == AUTARQUIA_FUNDACAO_FEDERAL || dadosProprietario.tipoProprietarioParcial == AUTARQUIA_FUNDACAO\" flex-xs=100 flex-sm=100><label>{{\'label-orgao-entidade\' | translate}}</label><input ng-model=dadosProprietario.orgaoEntidade disabled name=\"{{\'label-orgao-entidade\' | translate}}\"></md-input-container><md-input-container class=md-block ng-show=\"dadosProprietario.tipoProprietarioParcial == AUTARQUIA_FUNDACAO_FEDERAL || dadosProprietario.tipoProprietarioParcial == AUTARQUIA_FUNDACAO\" flex=33 flex-xs=100 flex-sm=100><label>{{\'label-ug-especifica\' | translate}}</label><input ng-model=dadosProprietario.unidadeUsuario name=\"{{\'label-ug-especifica\' | translate}}\" disabled></md-input-container></div><div layout=row ng-show=\"dadosProprietario.pessoas.length > 0\"><h3>{{\'title-terceiros\' | translate}}</h3></div><div layout=row><div layout=column><md-content flex ng-show=\"(dadosProprietario.tipoProprietarioReal.descricao==TERCEIROS || dadosProprietario.tipoProprietarioParcial.descricao==\'Terceiro\') && dadosProprietario.pessoas.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 10%\" md-column>{{\'tr-cpf-cnpj\' | translate}}</th><th style=\"width: 30%\" md-column>{{\'tr-nome-razao-social\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"pessoa in dadosProprietario.pessoas\"><td md-cell></td><td md-cell><label ng-bind=pessoa.nome></label></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=buscarImoveisLaudoRelatorio md-page-select></md-table-pagination></md-content></div></div></v-pane-content></v-pane></v-accordion></div></md-content></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/identificacao.tmpl.html","<ng-form name=formularioValidacao><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block layout-sm=column><label>{{\'label-rip\' | translate}}:</label><input ng-disabled=\"detalharLaudoRelatorio || isRipBloqueado\" ng-model=identificacao.rip maxlength=8 ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-rip\' | translate}}\" required ng-blur=consultarImovelPeloRipInformado();></md-input-container><md-input-container flex=50 class=rip-link><a href=\"\" ng-click=redirecionarConsultarImovelLaudo(); ng-show=\"!detalharLaudoRelatorio && !isRipBloqueado\">{{\'label-nao-sei-rip\'| translate}}</a></md-input-container></div><div><div layout=row layout-xs=column><md-checkbox class=md-primary ng-model=identificacao.laudoRelatorioDocumentoSei.isEnviarDocumento ng-change=changeLaudoAvaliacaoDocumentoSei() ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-enviar-documento-para-sei\' | translate}}</label></md-checkbox></div><div ng-if=identificacao.laudoRelatorioDocumentoSei.isEnviarDocumento layout=column flex><md-radio-group flex class=\"m-l-30 tamanho-radio-button-avaliacao md-block\" ng-model=identificacao.laudoRelatorioDocumentoSei.tipoEnviarDocumento ng-disabled=\"detalharLaudoRelatorio || !identificacao.laudoRelatorioDocumentoSei.isEnviarDocumento\" ng-change=changeLaudoAvaliacaoNumeroSei() ng-required=identificacao.laudoRelatorioDocumentoSei.isEnviarDocumento><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'CRIAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-criar-novo-processo-sei\' | translate}}</div></md-radio-button></div><div class=\"m-b-10 m-l-60\" layout=row ng-if=\"detalharLaudoRelatorio && identificacao.laudoRelatorioDocumentoSei.tipoEnviarDocumento == \'CRIAR_PROCESSO_SEI\' && identificacao.laudoRelatorioDocumentoSei.numeroProcessoSei\"><div class=texto-preto>{{\'label-n-processo\' | translate}}{{\': \' + identificacao.laudoRelatorioDocumentoSei.numeroProcessoSei | processoSei}}</div></div><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=100 ng-value=\"\'UTILIZAR_PROCESSO_SEI\'\"><div class=texto-preto>{{\'label-utilizar-processo-existente-sei\' | translate}}</div></md-radio-button></div></md-radio-group></div><md-input-container ng-if=\"identificacao.laudoRelatorioDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" class=\"md-block m-l-60\" flex=30 flex-xs=100 flex-sm=100><label>{{\'label-numero-processo-sei\' | translate}}</label><input type=text ui-mask=99999.999999/9999-99 ng-required=\"identificacao.laudoRelatorioDocumentoSei.tipoEnviarDocumento == \'UTILIZAR_PROCESSO_SEI\'\" ng-model=identificacao.laudoRelatorioDocumentoSei.numeroProcessoSei ng-disabled=detalharLaudoRelatorio ng-blur=validarLaudoAvaliacaoNumeroSeiProcessoSei()></md-input-container></div><div layout=row layout-xs=column class=m-t-10><md-input-container class=md-block flex=100><label>{{\'label-orgao\' | translate}}:</label><md-select id=orgao ng-model=identificacao.orgao name=\"{{\'label-orgao\' | translate}}\" ng-required=true ng-change=buscarListaUnidades() ng-disabled=\"detalharLaudoRelatorio || listaOrgaos.length == 1\"><md-option ng-repeat=\"item in listaOrgaos\" ng-value=item>{{item}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100><label>{{\'label-unidade\' | translate}}:</label><md-select id=unidade ng-model=identificacao.unidade name=\"{{\'label-unidade\' | translate}}\" ng-required=true ng-disabled=\"detalharLaudoRelatorio || listaUnidades.length == 1 || identificacao.orgao == null\"><md-option ng-repeat=\"item in listaUnidades\" ng-value=item>{{item}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-subunidade\' | translate}}</label><textarea rows=4 cols=50 ng-model=identificacao.subunidade ng-disabled=detalharLaudoRelatorio></textarea></md-input-container></div><responsavel-tecnico lista-responsaveis-tecnicos=listaResponsaveisTecnicos assinatura-nota-tecnica=false assinatura-laudo-relatorio=true detalhar-laudo-relatorio=detalharLaudoRelatorio></responsavel-tecnico><div layout=row layout-xs=column layout-sm=column class=m-t-20><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block layout-sm=column><label>{{\'label-solicitante\' | translate}}:</label><textarea class=barraRolagemTextArea maxlength=300 ng-disabled=detalharLaudoRelatorio ui-mask-placeholder-char=space ng-model=identificacao.solicitante required name=\"{{nomeCampo}} - {{\'label-solicitante\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block layout-sm=column><label>{{\'label-objetivo\' | translate}}:<md-tooltip md-direction=top>Exemplo: a) valor venal de mercado do imóvel avaliando b) valor locativo de mercado do imóvel avaliando etc</md-tooltip></label><textarea class=barraRolagemTextArea maxlength=3000 ng-disabled=detalharLaudoRelatorio ui-mask-placeholder-char=space ng-model=identificacao.objetivo required name=\"{{nomeCampo}} - {{\'label-objetivo\' | translate}}\"></textarea><span style=\"color: #cc0000; float: right\">{{0 + identificacao.objetivo.length}}/3000</span></md-input-container></div><div layout=row layout-xs=column layout-sm=column><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block layout-sm=column><label>{{\'label-finalidade\' | translate}}:</label><textarea class=barraRolagemTextArea maxlength=3000 ng-disabled=detalharLaudoRelatorio ui-mask-placeholder-char=space ng-model=identificacao.finalidade required name=\"{{nomeCampo}} - {{\'label-finalidade\' | translate}}\"></textarea><span style=\"color: #cc0000; float: right\">{{0 + identificacao.finalidade.length}}/3000</span></md-input-container></div><div layout=row flex=100 style=\"background-color: #fff3c7; margin-bottom: 20px\" ng-show=\"identificacao.rip && identificacao.tipo && aviso && !detalharLaudoRelatorio\"><div flex=10 layout=column><md-icon class=icon-aviso-aimportante ng-bind=\"\'info_outline\'\"></md-icon></div><div flex=90 layout=column style=\"margin-top: 5px\"><label style=\"color: #a7913e\">Importante</label><label style=\"color: #c7b46f\" ng-show=\"identificacao.tipo == LAUDO\">{{\'mensagem-existe-laudo-avaliacao\' | translate}}</label><label style=\"color: #c7b46f\" ng-show=\"identificacao.tipo == LAUDO_LOCACAO_ARRENDAMENTO\">{{\'mensagem-existe-laudo-locacao\' | translate}}</label><label style=\"color: #c7b46f\" ng-show=\"identificacao.tipo == \'RELATORIO\'\">{{\'mensagem-existe-relatorio-valor-referencia\' | translate}}</label></div></div><div layout=row><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tipo\' | translate}}:</label><md-select ng-disabled=detalharLaudoRelatorio ng-model=identificacao.tipo required name=\"{{nomeCampo}} - {{\'label-tipo\' | translate}}\" ng-change=changeTipoIdentificacao(); placeholder=\"{{\'label-tipo\' | translate}}\"><md-option ng-repeat=\"(k,v) in tiposIdentificacao\" ng-disabled=\"k == \'LAUDO_LOCACAO_ARRENDAMENTO\'\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100 ng-show=\"(identificacao.tipo==LAUDO || identificacao.tipo==LAUDO_LOCACAO_ARRENDAMENTO)\"><label>{{\'label-metodo-avaliacao\' | translate}}:</label><md-select ng-disabled=detalharLaudoRelatorio ng-model=identificacao.metodoAvaliacao ng-required=\"identificacao.tipo==LAUDO || identificacao.tipo==LAUDO_LOCACAO_ARRENDAMENTO\" name=\"{{nomeCampo}} - {{\'label-tipo\' | translate}}\" ng-change=\"changeMetodoAvaliacao(); limparDadosQuantificacaoCusto();\" placeholder=\"{{\'label-metodo-avaliacao\' | translate}}\"><md-option ng-repeat=\"(k,v) in metodosAvaliacao\" ng-disabled=\"k == \'INVOLUTIVO\' || k == \'CAPITALIZACAO_RENDA\'\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div layout=row ng-show=\"identificacao.metodoAvaliacao==EVOLUTIVO\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-avaliacao-terreno\' | translate}}:</label><md-select ng-disabled=\"detalharLaudoRelatorio  || identificacao.metodoAvaliacao==EVOLUTIVO\" ng-model=identificacao.tipoAvaliacaoTerreno ng-required=\"(identificacao.tipo==LAUDO || identificacao.tipo==LAUDO_LOCACAO_ARRENDAMENTO) && identificacao.metodoAvaliacao==EVOLUTIVO\" name=\"{{nomeCampo}} - {{\'label-avaliacao-terreno\' | translate}}\" placeholder=\"{{\'label-avaliacao-terreno\' | translate}}\"><md-option ng-repeat=\"(k,v) in tiposAvaliacaoTerreno\" ng-value=k ng-disabled=\"k!=\'COMPARATIVO\'\">{{v}}</md-option></md-select></md-input-container></div><div layout=row ng-show=\"identificacao.metodoAvaliacao == COMPARATIVO_MERCADO || identificacao.metodoAvaliacao==EVOLUTIVO\"><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-tratamento\' | translate}}:</label><md-select ng-disabled=\"detalharLaudoRelatorio || identificacao.metodoAvaliacao==EVOLUTIVO\" ng-model=identificacao.tratamento ng-required=\"(identificacao.tipo==LAUDO || identificacao.tipo==LAUDO_LOCACAO_ARRENDAMENTO) && (identificacao.metodoAvaliacao==COMPARATIVO_MERCADO || identificacao.metodoAvaliacao==EVOLUTIVO)\" name=\"{{nomeCampo}} - {{\'label-tratamento\' | translate}}\" placeholder=\"{{\'label-tratamento\' | translate}}\"><md-option ng-repeat=\"(k,v) in tratamentosIdentificacao\" ng-value=k ng-disabled=\"k!=\'CIENTIFICO\'\">{{v}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-ferramenta-analitica\' | translate}}:</label><md-select ng-disabled=\"detalharLaudoRelatorio || identificacao.metodoAvaliacao==EVOLUTIVO\" ng-model=identificacao.ferramentaAnalitica ng-required=\"(identificacao.tipo==LAUDO || identificacao.tipo==LAUDO_LOCACAO_ARRENDAMENTO) && (identificacao.metodoAvaliacao==COMPARATIVO_MERCADO || identificacao.metodoAvaliacao==EVOLUTIVO)\" name=\"{{nomeCampo}} - {{\'label-ferramenta-analitica\' | translate}}\" placeholder=\"{{\'label-ferramenta-analitica\' | translate}}\"><md-option ng-repeat=\"(k,v) in ferramentasAnaliticas\" ng-value=k ng-disabled=\"k!=\'REGRESSAO_LINEAR\'\">{{v}}</md-option></md-select></md-input-container></div><div layout=row ng-show=\"identificacao.metodoAvaliacao==EVOLUTIVO\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-quantificacao-custo-benfeitorias\' | translate}}:</label><md-select ng-disabled=detalharLaudoRelatorio ng-model=identificacao.quantificacaoCustoBenfeitoria ng-required=\"(identificacao.tipo==LAUDO || identificacao.tipo==LAUDO_LOCACAO_ARRENDAMENTO) && identificacao.metodoAvaliacao==EVOLUTIVO\" name=\"{{nomeCampo}} - {{\'label-quantificacao-custo-benfeitorias\' | translate}}\" placeholder=\"{{\'label-quantificacao-custo-benfeitorias\' | translate}}\"><md-option ng-repeat=\"(k,v) in listaQuantificacaoCustoBenfeitorias\" ng-value=k ng-disabled=\"k == \'CUB_PROJETO_DIFERENTE\'\">{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column ng-if=\"identificacao.tipo==\'RELATORIO\'\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-metodologia\' | translate}}:</label><md-select ng-disabled=detalharLaudoRelatorio ng-model=identificacao.metodologia ng-required=\"identificacao.tipo == \'RELATORIO\'\" name=\"{{nomeCampo}} - {{\'label-metodologia\' | translate}}\" ng-change=changeMetodologiaRelatorio() placeholder=\"{{\'label-metodologia\' | translate}}\"><md-option ng-repeat=\"(k,v) in listaMetodologia\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column layout-sm=column ng-show=\"identificacao.metodologia == \'OUTRO\'\"><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block layout-sm=column><label>{{\'label-especificar-outra-metodologia\' | translate}}:</label><textarea class=barraRolagemTextArea maxlength=3000 ng-disabled=detalharLaudoRelatorio ui-mask-placeholder-char=space ng-model=identificacao.metodologiaOutros ng-required=\"identificacao.metodologia == \'OUTRO\' && identificacao.tipo==\'RELATORIO\'\" name=\"{{nomeCampo}} - {{\'label-especificar-outra-metodologia\' | translate}}\"></textarea><span style=\"color: #cc0000; float: right\">{{0 + identificacao.metodologiaOutros.length}}/3000</span></md-input-container></div><fieldset class=fieldsetArredondado layout=column layout-xs=column><div layout=row flex layout-align=\"start start\" class=m-t-20><h3 class=subtitulo-pagina>{{\'label-normativos\' | translate}}</h3></div><div layout=row ng-show=!detalharLaudoRelatorio><md-input-container class=md-block flex=\"{{normativos.tipo == \'OUTROS\' ? \'50\' : \'100\'}}\"><label>{{\'label-norma\' | translate}}*:</label><md-select ng-model=normativos name=\"{{nomeCampo}} - Norma\" ng-disabled=detalharLaudoRelatorio ng-change=\"descricao.outros = null\"><md-option ng-repeat=\"item in listaTiposNorma\" ng-value=item>{{item.nome}}</md-option></md-select></md-input-container><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block layout-sm=column ng-if=\"normativos.tipo == \'OUTROS\'\"><label>{{\'label-descricao-outros\' | translate}}*:</label><textarea class=barraRolagemTextArea maxlength=3000 ng-disabled=detalharLaudoRelatorio ui-mask-placeholder-char=space ng-model=descricao.outros name=\"{{nomeCampo}} - {{\'label-descricao\' | translate}}\"></textarea><span style=\"color: #cc0000; float: right\">{{0 + identificacaodescricao.outros.length}}/3000</span></md-input-container></div><md-button ng-show=\"!detalharLaudoRelatorio && !editar.buttonShow\" class=\"md-primary md-raised\" ng-click=adicionarNorma() style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-adicionar\' | translate}}</label></md-button><div class=m-t-20 flex layout=row layout-align=end-end><md-button ng-show=\"!detalharLaudoRelatorio && editar.buttonShow\" class=\"md-primary md-raised\" ng-click=gravarAlteracaoNorma() style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-gravar\' | translate}}</label></md-button></div><div flex class=caixa-tabela-resultado ng-show=\"paginationNormas.length > 0\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 90%\" class=th-margin-pvgs md-column>{{\'label-norma\' | translate}}</th><th style=\"width: 10%\" ng-show=!detalharLaudoRelatorio class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in paginationNormas\"><td md-cell style=\"word-break: break-all\" ng-bind=\"item.norma.tipo != \'OUTROS\' ? item.norma.nome : item.descricao\"></td><td md-cell ng-show=!detalharLaudoRelatorio><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=alterarNorma($index) ng-show=\"!detalharLaudoRelatorio && item.norma.tipo == \'OUTROS\'\" class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-editar-norma\' | translate}}\" ng-bind=\"\'edit\'\" aria-label=\"{{\'label-editar-norma\' | translate}}\"><md-tooltip>{{\'label-editar-norma\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=removerNorma($index) ng-show=!detalharLaudoRelatorio class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir-norma\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir-norma\' | translate}}\"><md-tooltip>{{\'label-excluir-norma\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoNormas md-page-select></md-table-pagination></md-content></div></div></fieldset></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/quantificacao.tmpl.html","<ng-form name=formularioValidacao><div layout=column flex><div layout=column class=m-t-20 flex><documento-laudo-relatorio-padrao titulo=\"\'Arquivos de Orçamento\'\" lista-documentos-laudo=quantificacaoCusto.arquivosOrcamento campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Arquivos de Orçamento\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-descricao-orcamento-metodologia\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio required class=barraRolagemTextArea ng-model=quantificacaoCusto.descricaoOrcamento ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-orcamento-metodologia\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-custo-reproducao\' | translate}}</h3></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{benfeitoria.codigo + \'-\' + benfeitoria.nome}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-model=benfeitoria.valorCustoReproducao ui-mask-placeholder-char=space ui-number-mask=2 ng-change=\"benfeitoria.formatterValorCustoReproducao = convertNumeroPorExtenso(benfeitoria.valorCustoReproducao); calcularTotalvalorCustoReproducao(); calcularValorBenfeitoria();\"></md-input-container><label ng-bind=\"(benfeitoria.valorCustoReproducao ? benfeitoria.formatterValorCustoReproducao : \'\')\"></label></div><div class=m-b-20><div layout=row flex=100 layout-xs=column><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'label-custo-total\' | translate}}:</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{(valorCustoReproducaoTotal | currency).replace(\'R$\', \'\')}}</label></md-input-container><label ng-bind=\"(valorCustoReproducaoTotal ? formatterValorCustoReproducaoTotal : \'\')\"></label></div></div><div layout=row layout-xs=column class=m-t-20><h3 class=subtitulo-pagina>{{\'title-bdi\' | translate}}*</h3></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=quantificacaoCusto.tipoBdi name=\"{{nomeCampo}} - {{\'title-bdi\' | translate}}\" ng-change=calcularEnquadramentoGrauFundamentacao();><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposDbi\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div ng-if=quantificacaoCusto.tipoBdi><div layout=column class=m-t-20 flex ng-if=\"quantificacaoCusto.tipoBdi != \'ARBITRADO\'\"><documento-laudo-relatorio-padrao titulo=\"\'Fonte BDI\'\" lista-documentos-laudo=quantificacaoCusto.arquivosFonteBdi campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Fonte BDI\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-descricao-bdi-utilizado\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"quantificacaoCusto.tipoBdi != \'ARBITRADO\'\" class=barraRolagemTextArea ng-model=quantificacaoCusto.descricaoBdi ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-bdi-utilizado\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex=30 flex-xs flex-sm class=md-block><label>{{\'label-taxa-bdi\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-change=calcularValorBenfeitoria(); ng-model=quantificacaoCusto.valorTaxaBdi ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container><div flex=30 layout=column><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=quantificacaoCusto.isTaxaDbiUtilizadaOrcamento name=\"{{\'label-taxa-utilizada-orcamento\' | translate}}\"><label class=text-black>{{\'label-taxa-utilizada-orcamento\' | translate}}</label><md-tooltip md-direction=right>{{\'label-bdi-considerado-custo-reproducao\' | translate}}</md-tooltip></md-checkbox></div></div></div><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-depreciacao\' | translate}}</h3></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=quantificacaoCusto.tipoDepreciacao name=\"{{nomeCampo}} - {{\'title-depreciacao\' | translate}}\" ng-change=\"calcularEnquadramentoGrauFundamentacao(); limparDadosBenfeitoriaDepreciacao();\"><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposDepreciacao\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div ng-if=quantificacaoCusto.tipoDepreciacao><div layout=column class=m-t-20 flex ng-if=\"quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\'\"><documento-laudo-relatorio-padrao titulo=\"\'Fonte Depreciação\'\" lista-documentos-laudo=quantificacaoCusto.arquivosFonteDepreciacao campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Fonte Depreciacao\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{(quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\' ? \'label-descricao-depreciacao-calculada\' : \'label-descricao-depreciacao-arbitrada\') | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\'\" class=barraRolagemTextArea ng-model=quantificacaoCusto.descricaoDepreciacao ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-depreciacao\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><md-input-container layout-xs=row flex=30 flex-xs flex-sm class=md-block><label>{{\'label-taxa-depreciacao\' | translate}}:<md-tooltip md-direction=top>Percentual a ser descontado do custo de reprodução</md-tooltip></label><input ng-disabled=detalharLaudoRelatorio ng-change=calcularValorBenfeitoria(); required ng-model=quantificacaoCusto.valorTaxaDepreciacao ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div><div flex class=caixa-tabela-resultado ng-if=\"quantificacaoCusto.tipoDepreciacao == \'ROSS_HEIDECKE\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-vida-util\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-idade-aparente\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column><label>{{\'label-estado-conservacao\' | translate}}</label><md-icon style=cursor:pointer ng-click=showDialogDepreciacaoHeidecke($event) flex=10 ng-bind=\"\'info\'\" class=\"md-fab md-raised md-mini\"><md-tooltip>Depreciação de Heidecke</md-tooltip></md-icon></th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-taxa-depreciacao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=1 max=999 ng-model=benfeitoria.vidaUtil ng-change=calcularValorEstimado();calcularValorBenfeitoria(); required name=\"{{nomeCampo}} - {{\'label-vida-util-ano\' | translate}}\"></td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=0 max=999 ng-model=benfeitoria.idadeAparente ng-change=calcularValorEstimado();calcularValorBenfeitoria(); required name=\"{{nomeCampo}} - Idade Aparente\"></td><td md-cell class=th-margin-pvgs><md-select ng-disabled=detalharLaudoRelatorio ng-model=benfeitoria.estadoConservacao required ng-change=\"calcularValorEstimado(); calcularValorBenfeitoria();\"><md-option ng-repeat=\"item in listaEstadosConservacao\" ng-value=item>{{item.nome}}</md-option></md-select></td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorTaxaDepreciacao | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row flex flex-xs ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><label class=textoAccordionPvgLaudo>{{\'label-informacoes-contabilidade\' | translate}}</label></div><div flex ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-vida-util\' | translate}}*</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-idade-aparente\' | translate}}*</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-cub\' | translate}}*</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=1 max=999 ng-model=benfeitoria.vidaUtil ng-change=\"\" required name=\"{{nomeCampo}} - {{\'label-vida-util-ano\' | translate}}\"></td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=0 max=999 ng-model=benfeitoria.idadeAparente ng-change=\"\" required name=\"{{nomeCampo}} - Idade Aparente\"></td><td md-cell class=th-margin-pvgs><md-select ng-disabled=detalharLaudoRelatorio ng-model=benfeitoria.cubDepreciacao required ng-change=\"\"><md-option ng-repeat=\"cub in tiposCub\" ng-value=cub.nome>{{cub.nome}}</md-option></md-select></td></tr></tbody></table></md-table-container></md-content></div></div></div><div class=m-t-20 layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-valor-benfeitorias\' | translate}}</h3></div><div class=m-b-20 ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=md-block flex-gt-xs flex=100><span class=datepicker-label><span>{{benfeitoria.codigo + \'-\' + benfeitoria.nome}}:</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><label>{{(benfeitoria.valorCalculado | currency).replace(\'R$\', \'\')}}</label></div><div flex=70 class=md-block><label ng-bind=\"(benfeitoria.valorCalculado ? benfeitoria.formatterValorCalculadoBenfeitoria : \'\')\"></label></div></div></div></div><div class=m-b-20><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=md-block flex-gt-xs flex=100><span class=datepicker-label><span>{{\'label-custo-total\' | translate}}:</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><label>{{(valorCustoTotalBenfeitoria | currency).replace(\'R$\', \'\')}}</label></div><div flex=70 class=md-block><label ng-bind=\"(valorCustoTotalBenfeitoria ? formatterValorCustoTotalBenfeitoria : \'\')\"></label></div></div></div><div class=m-t-20 layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-grau-fundamentacao-metodo-quantificacao-custos-benfeitorias\' | translate}}</h3></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=10% md-column>{{\'title-item\' | translate}}</th><th width=15% md-column>{{\'title-descricao\' | translate}}</th><th width=25% md-column>{{\'title-grau-3\' | translate}}</th><th width=25% md-column>{{\'title-grau-2\' | translate}}</th><th width=25% md-column>{{\'title-grau-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>1</label></td><td md-cell><label>{{\'label-estimativa-custo-direto\' | translate}}</label></td><td md-cell ng-style=\"identificacao.quantificacaoCustoBenfeitoria == \'ORCAMENTO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custo-direto-grau-3\' | translate}}</label></td><td md-cell ng-style=\"identificacao.quantificacaoCustoBenfeitoria == \'CUB_PROJETO_SEMELHANTE\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custo-direto-grau-2\' | translate}}</label></td><td md-cell ng-style=\"identificacao.quantificacaoCustoBenfeitoria == \'CUB_PROJETO_DIFERENTE\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custo-direto-grau-1\' | translate}}</label></td></tr><tr md-row><td md-cell><label>2</label></td><td md-cell><label>{{\'label-bdi\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoBdi == \'CALCULADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-bdi-grau-3\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoBdi == \'JUSTIFICADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-bdi-grau-2\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoBdi == \'ARBITRADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-bdi-grau-1\' | translate}}</label></td></tr><tr md-row><td md-cell><label>3</label></td><td md-cell><label>{{\'label-depreciacao-fisica\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-depreciacao-fisica-grau-3\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoDepreciacao == \'ROSS_HEIDECKE\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-depreciacao-fisica-grau-2\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoDepreciacao == \'ARBITRADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-depreciacao-fisica-grau-1\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-enquadramento-grau-fundamentacao\' | translate}}</h3></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=25% md-column>{{\'title-graus\' | translate}}</th><th width=25% md-column>{{\'title-3\' | translate}}</th><th width=25% md-column>{{\'title-2\' | translate}}</th><th width=25% md-column>{{\'title-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>{{\'label-pontos-minimos\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrau3 ? colorTableColumnAvaliacao : {}\"><label>7</label></td><td md-cell ng-style=\"enquadramentoGrau2 ? colorTableColumnAvaliacao : {}\"><label>5</label></td><td md-cell ng-style=\"enquadramentoGrau1 ? colorTableColumnAvaliacao : {}\"><label>3</label></td></tr><tr md-row><td md-cell><label>{{\'label-itens-obrigatorios-grau-correspondente\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-3\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-2\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-1\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div class=\"pdfEspacamentoLabels m-t-10\"><label>{{\'title-grau-fundamentacao-metodo-quantificacao-custos\' | translate}}</label>&nbsp;<label class=negritoTexto ng-show=enquadramentoGrau3>{{\'title-grau-3\' | translate}}</label><label class=negritoTexto ng-show=enquadramentoGrau2>{{\'title-grau-2\' | translate}}</label><label class=negritoTexto ng-show=enquadramentoGrau1>{{\'title-grau-1\' | translate}}</label></div></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/quantificacaoCub.tmpl.html","<ng-form name=formularioValidacao><div layout=column flex><div layout=row layout-xs=column class=m-t-20><h3 class=subtitulo-pagina>{{\'title-area\' | translate}}</h3></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=quantificacaoCusto.tipoArea name=\"{{nomeCampo}} - {{\'title-area\' | translate}}\" ng-change=limparBenfeitoriaCub();><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposArea\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div flex class=caixa-tabela-resultado ng-if=\"quantificacaoCusto.tipoArea == \'AREA_REAL\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-cub\' | translate}}*</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs><md-select ng-disabled=detalharLaudoRelatorio ng-model=benfeitoria.cubDepreciacao required ng-change=calcularCustoConstrucao();><md-option ng-repeat=\"cub in tiposCub\" ng-value=cub.nome>{{cub.nome}}</md-option></md-select></td></tr></tbody></table></md-table-container></md-content></div></div><div flex class=caixa-tabela-resultado ng-if=\"quantificacaoCusto.tipoArea == \'AREA_EQUIVALENTE_CONSTRUCAO\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area-equivalente\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-cub-padrao\' | translate}}</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.areaEquivalente.areaEquivalenteConstrucao | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{benfeitoria.areaEquivalente.cubPadrao}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-show=\"benfeitoria.areaEquivalente.areaEquivalenteConstrucao && !detalharLaudoRelatorio\" ng-click=\"calcularAreaEquilente(benfeitoria, false)\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-show=\"!benfeitoria.areaEquivalente.areaEquivalenteConstrucao && !detalharLaudoRelatorio\" ng-click=\"calcularAreaEquilente(benfeitoria, false)\"><md-icon ng-bind=\"\'add\'\"><md-tooltip>{{\'label-calcular-area-equivalente\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-show=benfeitoria.areaEquivalente.areaEquivalenteConstrucao ng-click=\"calcularAreaEquilente(benfeitoria, true)\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>{{\'label-detalhar\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row layout-xs=column class=m-t-20><h3 class=subtitulo-pagina>{{\'title-estimacao-custos\' | translate}}</h3></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=quantificacaoCusto.tipoEstimativaCusto name=\"{{nomeCampo}} - {{\'title-estimacao-custos\' | translate}}\" ng-change=\"\"><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposEstimativaCusto\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div flex class=caixa-tabela-resultado ng-if=quantificacaoCusto.tipoEstimativaCusto><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 80%\" class=th-margin-pvgs md-column></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs ng-if=\"quantificacaoCusto.tipoEstimativaCusto == \'UTILIZAR_ORCAMENTOS\'\"><table class=no-border md-table><tr class=no-border md-row><td class=no-border style=\"width: 40%\" md-cell>{{\'label-orcamento-elevadores\' | translate}}</td><td class=no-border style=\"width: 60%\" md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=\"md-block m-t-5 m-r-10\"><label ng-bind=\"\'R$\'\"></label></div><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoElevadores ui-mask-placeholder-char=space ui-number-mask=2></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"(benfeitoria.valorOrcamentoElevadores > 0 ? convertNumeroPorExtenso(benfeitoria.valorOrcamentoElevadores) : \'\')\"></label></div></div></td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-instalacoes-especiais\' | translate}}</td><td class=no-border md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=\"md-block m-t-5 m-r-10\"><label ng-bind=\"\'R$\'\"></label></div><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoInstalacoesEspeciais ui-mask-placeholder-char=space ui-number-mask=2></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"(benfeitoria.valorOrcamentoInstalacoesEspeciais > 0 ? convertNumeroPorExtenso(benfeitoria.valorOrcamentoInstalacoesEspeciais) : \'\')\"></label></div></div></td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-fundacoes-especiais\' | translate}}</td><td class=no-border md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=\"md-block m-t-5 m-r-10\"><label ng-bind=\"\'R$\'\"></label></div><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoFundacoesEspeciais ui-mask-placeholder-char=space ui-number-mask=2></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"(benfeitoria.valorOrcamentoFundacoesEspeciais > 0 ? convertNumeroPorExtenso(benfeitoria.valorOrcamentoFundacoesEspeciais) : \'\')\"></label></div></div></td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-orcamento-fundacoes-diretas\' | translate}}</td><td class=no-border md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=\"md-block m-t-5 m-r-10\"><label ng-bind=\"\'R$\'\"></label></div><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoFundacoesDiretas ui-mask-placeholder-char=space ui-number-mask=2></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"(benfeitoria.valorOrcamentoFundacoesDiretas > 0 ? convertNumeroPorExtenso(benfeitoria.valorOrcamentoFundacoesDiretas) : \'\')\"></label></div></div></td></tr></table></td><td md-cell class=th-margin-pvgs ng-if=\"quantificacaoCusto.tipoEstimativaCusto == \'APLICAR_PERCENTUAL\'\"><table class=no-border md-table><tr class=no-border md-row><td class=no-border style=\"width: 40%\" md-cell>{{\'label-elevadores\' | translate}}</td><td class=no-border style=\"width: 60%\" md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoElevadores ui-mask-placeholder-char=space ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"\'%\'\"></label></div></div></td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-instalacoes-especiais\' | translate}}</td><td class=no-border md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoInstalacoesEspeciais ui-mask-placeholder-char=space ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"\'%\'\"></label></div></div></td></tr><tr class=no-border md-row><td class=no-border md-cell>{{\'label-fundacoes-especiais\' | translate}}</td><td class=no-border md-cell><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><input ng-disabled=detalharLaudoRelatorio required ng-blur=calcularCustoConstrucao(); ng-model=benfeitoria.valorOrcamentoFundacoesEspeciais ui-mask-placeholder-char=space ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div><div flex=70 class=\"md-block m-t-5 m-l-10\"><label ng-bind=\"\'%\'\"></label></div></div></td></tr></table></td></tr></tbody></table></md-table-container></md-content></div></div><div layout=column class=m-t-10 flex><documento-laudo-relatorio-padrao titulo=\"\'Orçamentos/Fonte\'\" lista-documentos-laudo=quantificacaoCusto.arquivosOrcamento campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Orçamento/Fonte de Custos\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=column class=m-t-20><h3 class=subtitulo-pagina>{{\'title-custo-construcao\' | translate}}</h3></div><div flex class=caixa-tabela-resultado style=\"margin-top: -10px\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-area\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-custo-construcao\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-estimativa\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.area | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorCustoConstrucao | currency).replace(\'R$\', \'\')}}</td><td md-cell class=th-margin-pvgs>{{((benfeitoria.area * benfeitoria.valorCustoConstrucao) | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row layout-xs=column class=m-t-20><h3 class=subtitulo-pagina>{{\'title-bdi\' | translate}}*</h3></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=quantificacaoCusto.tipoBdi name=\"{{nomeCampo}} - {{\'title-bdi\' | translate}}\" ng-change=calcularEnquadramentoGrauFundamentacao();><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposDbi\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div ng-if=quantificacaoCusto.tipoBdi><div layout=column class=m-t-20 flex ng-if=\"quantificacaoCusto.tipoBdi != \'ARBITRADO\'\"><documento-laudo-relatorio-padrao titulo=\"\'Fonte BDI\'\" lista-documentos-laudo=quantificacaoCusto.arquivosFonteBdi campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Fonte BDI\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-descricao-bdi-utilizado\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"quantificacaoCusto.tipoBdi != \'ARBITRADO\'\" class=barraRolagemTextArea ng-model=quantificacaoCusto.descricaoBdi ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-bdi-utilizado\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex=30 flex-xs flex-sm class=md-block><label>{{\'label-taxa-bdi\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-change=calcularValorBenfeitoria(); ng-model=quantificacaoCusto.valorTaxaBdi ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div></div><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-depreciacao\' | translate}}</h3></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=quantificacaoCusto.tipoDepreciacao name=\"{{nomeCampo}} - {{\'title-depreciacao\' | translate}}\" ng-change=calcularEnquadramentoGrauFundamentacao();limparDadosBenfeitoriaDepreciacao();><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposDepreciacao\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div ng-if=quantificacaoCusto.tipoDepreciacao><div layout=column class=m-t-20 flex ng-if=\"quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\'\"><documento-laudo-relatorio-padrao titulo=\"\'Fonte Depreciação\'\" lista-documentos-laudo=quantificacaoCusto.arquivosFonteDepreciacao campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Fonte Depreciacao\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{(quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\' ? \'label-descricao-depreciacao-calculada\' : \'label-descricao-depreciacao-arbitrada\') | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\'\" class=barraRolagemTextArea ng-model=quantificacaoCusto.descricaoDepreciacao ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-depreciacao\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><md-input-container layout-xs=row flex=30 flex-xs flex-sm class=md-block><label>{{\'label-taxa-depreciacao\' | translate}}:<md-tooltip md-direction=top>Percentual a ser descontado do custo de reprodução</md-tooltip></label><input ng-disabled=detalharLaudoRelatorio ng-change=calcularValorBenfeitoria(); required ng-model=quantificacaoCusto.valorTaxaDepreciacao ui-mask-placeholder-char=space ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div><div flex class=caixa-tabela-resultado ng-if=\"quantificacaoCusto.tipoDepreciacao == \'ROSS_HEIDECKE\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-vida-util\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-idade-aparente\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column><label>{{\'label-estado-conservacao\' | translate}}</label><md-icon style=cursor:pointer ng-click=showDialogDepreciacaoHeidecke($event) flex=10 ng-bind=\"\'info\'\" class=\"md-fab md-raised md-mini\"><md-tooltip>Depreciação de Heidecke</md-tooltip></md-icon></th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-taxa-depreciacao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=1 max=999 ng-model=benfeitoria.vidaUtil ng-change=calcularValorEstimado();calcularValorBenfeitoria(); required name=\"{{nomeCampo}} - {{\'label-vida-util-ano\' | translate}}\"></td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=0 max=999 ng-model=benfeitoria.idadeAparente ng-change=calcularValorEstimado();calcularValorBenfeitoria(); required name=\"{{nomeCampo}} - Idade Aparente\"></td><td md-cell class=th-margin-pvgs><md-select ng-disabled=detalharLaudoRelatorio ng-model=benfeitoria.estadoConservacao required ng-change=\"calcularValorEstimado(); calcularValorBenfeitoria();\"><md-option ng-repeat=\"item in listaEstadosConservacao\" ng-value=item>{{item.nome}}</md-option></md-select></td><td md-cell class=th-margin-pvgs>{{(benfeitoria.valorTaxaDepreciacao | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row flex flex-xs ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><label class=textoAccordionPvgLaudo>{{\'label-informacoes-contabilidade\' | translate}}</label></div><div flex ng-if=\"quantificacaoCusto.tipoDepreciacao != \'ROSS_HEIDECKE\'\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 33%\" class=th-margin-pvgs md-column>{{\'label-benfeitoria\' | translate}}</th><th style=\"width: 33%\" class=th-margin-pvgs md-column>{{\'label-vida-util\' | translate}}*</th><th style=\"width: 33%\" class=th-margin-pvgs md-column>{{\'label-idade-aparente\' | translate}}*</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><td md-cell class=th-margin-pvgs>{{benfeitoria.codigo + \'-\' + benfeitoria.tipoBenfeitoria.descricao}}</td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=1 max=999 ng-model=benfeitoria.vidaUtil ng-change=\"\" required name=\"{{nomeCampo}} - {{\'label-vida-util-ano\' | translate}}\"></td><td md-cell class=th-margin-pvgs><input ng-disabled=detalharLaudoRelatorio type=text maxlength=3 ui-number-mask=0 min=0 max=999 ng-model=benfeitoria.idadeAparente ng-change=\"\" required name=\"{{nomeCampo}} - Idade Aparente\"></td></tr></tbody></table></md-table-container></md-content></div></div></div><div class=m-t-20 layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-valor-benfeitorias\' | translate}}</h3></div><div class=m-b-30 ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\"><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=md-block flex-gt-xs flex=100><span class=datepicker-label><span>{{benfeitoria.codigo + \'-\' + benfeitoria.nome}}:</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><label>{{(benfeitoria.valorCalculado | currency).replace(\'R$\', \'\')}}</label></div><div flex=70 class=md-block><label ng-bind=\"(benfeitoria.valorCalculado ? benfeitoria.formatterValorCalculadoBenfeitoria : \'\')\"></label></div></div></div><div class=m-b-30><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=md-block flex-gt-xs flex=100><span class=datepicker-label><span>{{\'label-custo-total\' | translate}}:</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><label>{{(valorCustoTotalBenfeitoria | currency).replace(\'R$\', \'\')}}</label></div><div flex=70 class=md-block><label ng-bind=\"(valorCustoTotalBenfeitoria ? formatterValorCustoTotalBenfeitoria : \'\')\"></label></div></div></div><div class=m-t-20 layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-grau-fundamentacao-metodo-quantificacao-custos-benfeitorias\' | translate}}</h3></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=10% md-column>{{\'title-item\' | translate}}</th><th width=15% md-column>{{\'title-descricao\' | translate}}</th><th width=25% md-column>{{\'title-grau-3\' | translate}}</th><th width=25% md-column>{{\'title-grau-2\' | translate}}</th><th width=25% md-column>{{\'title-grau-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>1</label></td><td md-cell><label>{{\'label-estimativa-custo-direto\' | translate}}</label></td><td md-cell ng-style=\"identificacao.quantificacaoCustoBenfeitoria == \'ORCAMENTO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custo-direto-grau-3\' | translate}}</label></td><td md-cell ng-style=\"identificacao.quantificacaoCustoBenfeitoria == \'CUB_PROJETO_SEMELHANTE\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custo-direto-grau-2\' | translate}}</label></td><td md-cell ng-style=\"identificacao.quantificacaoCustoBenfeitoria == \'CUB_PROJETO_DIFERENTE\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custo-direto-grau-1\' | translate}}</label></td></tr><tr md-row><td md-cell><label>2</label></td><td md-cell><label>{{\'label-bdi\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoBdi == \'CALCULADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-bdi-grau-3\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoBdi == \'JUSTIFICADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-bdi-grau-2\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoBdi == \'ARBITRADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-bdi-grau-1\' | translate}}</label></td></tr><tr md-row><td md-cell><label>3</label></td><td md-cell><label>{{\'label-depreciacao-fisica\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoDepreciacao == \'CUSTO_RECUPERACAO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-depreciacao-fisica-grau-3\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoDepreciacao == \'ROSS_HEIDECKE\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-depreciacao-fisica-grau-2\' | translate}}</label></td><td md-cell ng-style=\"quantificacaoCusto.tipoDepreciacao == \'ARBITRADO\' ? colorTableColumnAvaliacao : {}\"><label>{{\'label-depreciacao-fisica-grau-1\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-enquadramento-grau-fundamentacao\' | translate}}</h3></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=25% md-column>{{\'title-graus\' | translate}}</th><th width=25% md-column>{{\'title-3\' | translate}}</th><th width=25% md-column>{{\'title-2\' | translate}}</th><th width=25% md-column>{{\'title-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>{{\'label-pontos-minimos\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrau3 ? colorTableColumnAvaliacao : {}\"><label>7</label></td><td md-cell ng-style=\"enquadramentoGrau2 ? colorTableColumnAvaliacao : {}\"><label>5</label></td><td md-cell ng-style=\"enquadramentoGrau1 ? colorTableColumnAvaliacao : {}\"><label>3</label></td></tr><tr md-row><td md-cell><label>{{\'label-itens-obrigatorios-grau-correspondente\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-3\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-2\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-1\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div class=\"pdfEspacamentoLabels m-t-10\"><label>{{\'title-grau-fundamentacao-metodo-quantificacao-custos\' | translate}}</label>&nbsp;<label class=negritoTexto ng-show=enquadramentoGrau3>{{\'title-grau-3\' | translate}}</label><label class=negritoTexto ng-show=enquadramentoGrau2>{{\'title-grau-2\' | translate}}</label><label class=negritoTexto ng-show=enquadramentoGrau1>{{\'title-grau-1\' | translate}}</label></div></div></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/resultadoAvaliacaoLaudoEvolutivo.tmpl.html","<ng-form name=formularioValidacao><div layout=column flex><div class=\"m-b-30 m-t-20\"><div layout=row flex=100 layout-xs=column><div class=md-block flex-gt-xs flex=100><span class=datepicker-label><span>{{\'label-valor-terreno-r\' | translate}}:</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><label>{{(valorTerreno | currency).replace(\'R$\', \'\')}}</label></div><div flex=70 class=md-block><label ng-bind=\"(valorTerreno ? formatterValorTerreno : \'\')\"></label></div></div></div><div class=m-b-30 ng-repeat=\"benfeitoria in quantificacaoCusto.benfeitorias\" ng-if=\"avaliacao.tipoValorLaudo != OUTRA_FONTE\"><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=md-block flex-gt-xs flex=100><span class=datepicker-label><span>{{benfeitoria.codigo + \'-\' + benfeitoria.nome}}:</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><label>{{(benfeitoria.valorCalculado | currency).replace(\'R$\', \'\')}}</label></div><div flex=70 class=md-block><label ng-bind=\"(benfeitoria.valorCalculado ? benfeitoria.formatterValorCalculadoBenfeitoria : \'\')\"></label></div></div></div><div class=m-b-30 ng-repeat=\"benfeitoria in listaBenfeitoriaAvaliacao\" ng-if=\"avaliacao.tipoValorLaudo == OUTRA_FONTE\"><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div class=md-block flex-gt-xs flex=100><span class=datepicker-label><span>{{benfeitoria.benfeitoriaImovel.codigo + \'-\' + benfeitoria.benfeitoriaImovel.nome}}:</span></span></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><div flex=30 class=md-block><label>{{(benfeitoria.benfeitoriaImovel.valorEdificacao.valor | currency).replace(\'R$\', \'\')}}</label></div><div flex=70 class=md-block><label ng-bind=\"(benfeitoria.benfeitoriaImovel.valorEdificacao.valor ? benfeitoria.benfeitoriaImovel.valorEdificacao.formatterValorEdificacao : \'\')\"></label></div></div></div><div layout=row layout-xs=column class=m-t-20><h3 class=subtitulo-pagina>{{\'title-fator-comercializacao\' | translate}} *</h3></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=resultadoAvaliacao.tipoFatorComercializacao name=\"{{nomeCampo}} - {{\'title-fator-comercializacao\' | translate}}\" ng-change=calcularGrauFundamentacaoMetodoEvolutivo();><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"(k,v) in listaTiposFatorComercializacao\" ng-value=k><label class=texto-preto>{{v}}</label></md-radio-button></div></md-radio-group></md-input></div><div ng-show=resultadoAvaliacao.tipoFatorComercializacao><div layout=column class=m-t-20 flex ng-show=\"resultadoAvaliacao.tipoFatorComercializacao != \'ARBITRADO\'\"><documento-laudo-relatorio-padrao titulo=\"\'Fonte Fator de Comercialização\'\" lista-documentos-laudo=resultadoAvaliacao.arquivosFatorComercializacao campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_02\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Fonte Fator de Comercialização\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'label-descricao-fator-comercializacao\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio ng-required=\"resultadoAvaliacao.tipoFatorComercializacao != \'ARBITRADO\' && identificacao.metodologia != \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\'\" class=barraRolagemTextArea ng-model=resultadoAvaliacao.descricaoFatorComercializacao ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'label-descricao-fator-comercializacao\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex=30 flex-xs flex-sm class=md-block><label>{{\'label-fc\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-blur=\"calcularValorImovel(); calcularGrauFundamentacaoMetodoEvolutivo();\" ng-model=resultadoAvaliacao.valorFc ui-number-mask=2 min=0 max=999.99 maxlength=6></md-input-container></div><div layout=row layout-xs=column class=m-t-20><h3 class=subtitulo-pagina>{{\'title-valor-imovel\' | translate}} *</h3></div><div class=m-b-20><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{avaliacao.resultadosAvaliacao.moeda.simbolo + \' \' +(resultadoAvaliacao.valorImovel | currency).replace(\'R$\', \'\')}}</label></md-input-container><label ng-bind=\"(resultadoAvaliacao.valorImovel ? formatterValorImovel : \'\')\"></label></div></div></div><div ng-if=\"identificacao.metodologia != \'ESTIMATIVA_VALORES_TERRENO_BENFEITORIA\'\"><div class=m-t-20 layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-grau-fundamentacao-metodo-evolutivo\' | translate}}</h3></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=10% md-column>{{\'title-item\' | translate}}</th><th width=15% md-column>{{\'title-descricao\' | translate}}</th><th width=25% md-column>{{\'title-grau-3\' | translate}}</th><th width=25% md-column>{{\'title-grau-2\' | translate}}</th><th width=25% md-column>{{\'title-grau-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>1</label></td><td md-cell><label>{{\'label-estimativa-valor-terreno\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaValorTerrenoGrau3 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-valor-terreno-grau-3\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaValorTerrenoGrau2 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-valor-terreno-grau-2\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaValorTerrenoGrau1 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-valor-terreno-grau-1\' | translate}}</label></td></tr><tr md-row><td md-cell><label>2</label></td><td md-cell><label>{{\'label-estimativa-custos-reedicao\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaCustosReedicaoGrau3 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custos-reedicao-grau-3\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaCustosReedicaoGrau2 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custos-reedicao-grau-2\' | translate}}</label></td><td md-cell ng-style=\"itemEstimativaCustosReedicaoGrau1 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-estimativa-custos-reedicao-grau-1\' | translate}}</label></td></tr><tr md-row><td md-cell><label>3</label></td><td md-cell><label>{{\'label-fator-comercializacao\' | translate}}</label></td><td md-cell ng-style=\"itemFatorComercializacaoGrau3 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-fator-comercializacao-grau-3\' | translate}}</label></td><td md-cell ng-style=\"itemFatorComercializacaoGrau2 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-fator-comercializacao-grau-2\' | translate}}</label></td><td md-cell ng-style=\"itemFatorComercializacaoGrau1 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-fator-comercializacao-grau-1\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'title-enquadramento-grau-fundamentacao\' | translate}}</h3></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=25% md-column>{{\'title-graus\' | translate}}</th><th width=25% md-column>{{\'title-3\' | translate}}</th><th width=25% md-column>{{\'title-2\' | translate}}</th><th width=25% md-column>{{\'title-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>{{\'label-pontos-minimos\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrau3 ? colorTableColumnAvaliacao : {}\"><label>8</label></td><td md-cell ng-style=\"enquadramentoGrau2 ? colorTableColumnAvaliacao : {}\"><label>5</label></td><td md-cell ng-style=\"enquadramentoGrau1 ? colorTableColumnAvaliacao : {}\"><label>3</label></td></tr><tr md-row><td md-cell><label>{{\'label-itens-obrigatorios-grau-correspondente\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-3-resultado-avaliacao\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-2\' | translate}}</label></td><td md-cell><label>{{\'label-enquadramento-grau-1\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div class=\"pdfEspacamentoLabels m-t-10\"><label>{{\'title-grau-fundamentacao-laudo-avaliacao\' | translate}}:</label>&nbsp;<label class=negritoTexto ng-show=enquadramentoGrau3>{{\'title-grau-3\' | translate}}</label><label class=negritoTexto ng-show=enquadramentoGrau2>{{\'title-grau-2\' | translate}}</label><label class=negritoTexto ng-show=enquadramentoGrau1>{{\'title-grau-1\' | translate}}</label></div></div><div class=m-t-20 layout-xs=row flex flex-xs flex-sm><md-input-container layout-xs=row flex-xs flex-sm class=md-block><label>{{\'title-encerramento\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio class=barraRolagemTextArea ng-model=resultadoAvaliacao.encerramento ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-encerramento\' | translate}}\"></textarea></md-input-container></div></div></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/resultados.tmpl.html","<ng-form name=formularioValidacao><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header class=textoAccordionPvgLaudo>{{\'title-resultados-da-avaliacao\' | translate}}</v-pane-header><v-pane-content><div layout=column flex><div layout=row flex flex-xs><label class=textoAccordionPvgLaudo>{{\'title-valores\' | translate}}</label></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-modelo-adotado\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio required class=barraRolagemTextArea ng-model=avaliacao.resultadosAvaliacao.modeloAdotado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-modelo-adotado\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container class=md-block flex flex-xs flex-sm><label>{{\'title-moeda\' | translate}}:</label><md-select ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.resultadosAvaliacao.moeda name=\"{{nomeCampo}} - {{\'title-moeda\' | translate}}\" placeholder=\"{{\'title-moeda\' | translate}}\"><md-option ng-repeat=\"moeda in listaMoedas\" ng-value=moeda ng-selected=\"moeda.id==1\">{{moeda.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-valor-medio\' | translate}}:</label><input ng-disabled=\"detalharLaudoRelatorio || !avaliacao.resultadosAvaliacao.moeda\" required ng-model=avaliacao.resultadosAvaliacao.valorMedio ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-valor-medio\' | translate}}\" ui-number-mask=2 ng-change=\"formatterValorMedio = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.valorMedio);calcularVariacaoValorAdotado(); calcularAmplitude();\" ng-blur=validarValores();></md-input-container><label style=\"margin-top: 20px\" ng-bind=\"(avaliacao.resultadosAvaliacao.valorMedio ? formatterValorMedio : \'\')\"></label></div><div class=m-b-20 layout=row layout-xs=column ng-show=avaliacao.resultadosAvaliacao.valorMedio><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((avaliacao.resultadosAvaliacao.valorMedio/areaTotalTerreno) | currency).replace(\'R$\', \'\')}} {{avaliacao.resultadosAvaliacao.moeda ? avaliacao.resultadosAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-valor-minimo\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.resultadosAvaliacao.valorMinimo ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-valor-minimo\' | translate}}\" ui-number-mask=2 ng-change=\"formatterValorMinimo = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.valorMinimo); determinarValorAdotadoSelecionado();calcularAmplitude()\" ng-blur=validarValores()></md-input-container><label ng-bind=\"(avaliacao.resultadosAvaliacao.valorMinimo ? formatterValorMinimo : \'\')\"></label></div><div class=m-b-20 layout=row layout-xs=column ng-show=avaliacao.resultadosAvaliacao.valorMinimo><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((avaliacao.resultadosAvaliacao.valorMinimo/areaTotalTerreno) | currency).replace(\'R$\', \'\')}} {{avaliacao.resultadosAvaliacao.moeda ? avaliacao.resultadosAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-valor-maximo\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.resultadosAvaliacao.valorMaximo ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-valor-maximo\' | translate}}\" ui-number-mask=2 ng-change=\"formatterValorMaximo = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.valorMaximo); determinarValorAdotadoSelecionado();calcularAmplitude()\" ng-blur=validarValores()></md-input-container><label ng-bind=\"(avaliacao.resultadosAvaliacao.valorMaximo ? formatterValorMaximo : \'\')\"></label></div><div class=m-b-20 layout=row layout-xs=column ng-show=avaliacao.resultadosAvaliacao.valorMaximo><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((avaliacao.resultadosAvaliacao.valorMaximo/areaTotalTerreno) | currency).replace(\'R$\', \'\')}} {{avaliacao.resultadosAvaliacao.moeda ? avaliacao.resultadosAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><label class=\"sidenavTitulos deslocamentoTituloData font\">{{\'title-valor-adotado\' | translate}}:</label><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=avaliacao.resultadosAvaliacao.tipoValor name=\"{{nomeCampo}} - {{\'title-valor-adotado\' | translate}}\" ng-change=calcularVariacaoValorAdotado();><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"tipoValor in listaTiposValores | orderBy: \'ordem\'\" ng-value=tipoValor.name><label class=texto-preto>{{tipoValor.descricao}}</label></md-radio-button></div></md-radio-group></md-input></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm ng-if=\"avaliacao.resultadosAvaliacao.tipoValor==OUTRO_VALOR\"><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-outro-valor-adotado\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio ng-model=avaliacao.resultadosAvaliacao.outroValorAdotado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-outro-valor-adotado\' | translate}}\" ui-number-mask=2 ng-change=\"formatterOutroValorAdotado = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.outroValorAdotado);calcularVariacaoValorAdotado();\" ng-required=\"avaliacao.resultadosAvaliacao.tipoValor==OUTRO_VALOR\"></md-input-container><label ng-bind=\"(avaliacao.resultadosAvaliacao.outroValorAdotado ? formatterOutroValorAdotado : \'\')\"></label></div><div class=m-b-20 layout=row layout-xs=column ng-show=\"avaliacao.resultadosAvaliacao.outroValorAdotado && avaliacao.resultadosAvaliacao.tipoValor==OUTRO_VALOR\"><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((avaliacao.resultadosAvaliacao.outroValorAdotado/areaTotalTerreno) | currency).replace(\'R$\', \'\')}} {{avaliacao.resultadosAvaliacao.moeda ? avaliacao.resultadosAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout-xs=row flex=50 flex-xs flex-sm ng-if=\"avaliacao.resultadosAvaliacao.tipoValor!=VALOR_MEDIO &&  valorAdotadoSelecionado && (valorAdotadoSelecionado < variacaoInferiorValorMedio || valorAdotadoSelecionado > variacaoSuperiorValorMedio)\"><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-justificativa-do-valor-adotado\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio class=barraRolagemTextArea ng-model=avaliacao.resultadosAvaliacao.justificativaValorAdotado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-justificativa-do-valor-adotado\' | translate}}\" ng-required=\"avaliacao.resultadosAvaliacao.tipoValor!=VALOR_MEDIO && valorAdotadoSelecionado && (valorAdotadoSelecionado < variacaoInferiorValorMedio || valorAdotadoSelecionado > variacaoSuperiorValorMedio)\">\n\n                            </textarea></md-input-container></div><div layout=column class=m-t-20 flex><documento-laudo-relatorio-padrao hint=\"\'Devem ser anexados os resultados do modelo estatístico\'\" titulo=\"\'Relatórios Estatísticos\'\" lista-documentos-laudo=avaliacao.resultadosAvaliacao.relatoriosEstatisticos campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_02 tipo-anexo=\"\'Relatórios Estatísticos\'\" anexo-laudo-relatorio=anexoLaudoRelatorio detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=avaliacao.resultadosAvaliacao.isArquivoModelo ng-change=limparDadosArquivosModelo(); name=\"{{\'label-incluir-arquivo-modelo\' | translate}}\" ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-incluir-arquivo-modelo\' | translate}}<md-tooltip md-direction=top>Adicionar arquivo do software utilizado para o cálculo</md-tooltip></label></md-checkbox></div><div layout=column flex ng-if=avaliacao.resultadosAvaliacao.isArquivoModelo><documento-laudo-relatorio-padrao hint=\"\'Adicionar arquivo do software utilizado para o cálculo\'\" titulo=\"\'Arquivos do Modelo\'\" lista-documentos-laudo=avaliacao.resultadosAvaliacao.arquivosModelo campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_03\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row flex flex-xs><label class=textoAccordionPvgLaudo>{{\'title-esquadramento\' | translate}}</label></div><div layout=row layout-xs=column flex-sm=column><md-container class=md-block flex flex-xs flex-sm><br><div layout=row flex><div flex layout=column flex-offset=5><md-checkbox ng-disabled=detalharLaudoRelatorio ng-change=medirGrauEnquadramentoFundamentacao() class=md-primary ng-model=avaliacao.enquadramento.isApresentacaoLaudoModalidade name=\"{{\'title-checkbox1-laudo-mobilidade\' | translate}}\"><label class=text-black>{{\'title-checkbox1-laudo-mobilidade\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio ng-change=medirGrauEnquadramentoFundamentacao() class=md-primary ng-model=avaliacao.enquadramento.isApresentacaoAnaliseLaudo name=\"{{\'title-checkbox2-analise-modelo\' | translate}}\"><label class=text-black>{{\'title-checkbox2-analise-modelo\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio ng-change=medirGrauEnquadramentoFundamentacao() class=md-primary ng-model=avaliacao.enquadramento.isIdenticacaoCompletaEnderecos name=\"{{\'title-checkbox3-idenfificacao-completa\' | translate}}\"><label class=text-black>{{\'title-checkbox3-idenfificacao-completa\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=avaliacao.enquadramento.isAdocaoEstimativaTendencia name=\"{{\'title-checkbox4-estimativa\' | translate}}\" disabled ng-checked=icAdocaoEstimativa><label class=text-black>{{\'title-checkbox4-estimativa\' | translate}}</label></md-checkbox></div></div></md-container></div><enquadramento-laudo-grau-fundamentacao avaliacao=avaliacao detalhar-laudo-relatorio=detalharLaudoRelatorio medir-grau-enquadramento-fundamentacao=medirGrauEnquadramentoFundamentacao enquadramento-grau3=enquadramentoGrau3 enquadramento-grau2=enquadramentoGrau2 enquadramento-grau1=enquadramentoGrau1 enquadramento-grau-nao-atingido=enquadramentoGrauNaoAtingido></enquadramento-laudo-grau-fundamentacao><br><enquadramento-laudo-grau-precisao avaliacao=avaliacao detalhar-laudo-relatorio=detalharLaudoRelatorio></enquadramento-laudo-grau-precisao><br><div layout-xs=row flex flex-xs flex-sm ng-if=\"avaliacao.enquadramento.amplitudeIntervalo > 50\"><md-input-container layout-xs=row flex-xs flex-sm class=md-block><label>{{\'title-justificativa-nao-enquadramento-laudo\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio class=barraRolagemTextArea ng-model=avaliacao.enquadramento.justificativaNaoEnquadramento ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-justificativa-nao-enquadramento-laudo\' | translate}}\" ng-required=\"avaliacao.enquadramento.amplitudeIntervalo > 50\"></textarea></md-input-container></div><div layout-xs=row flex flex-xs flex-sm ng-show=\"identificacao.metodoAvaliacao != \'EVOLUTIVO\'\"><md-input-container layout-xs=row flex-xs flex-sm class=md-block><label>{{\'title-encerramento\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio class=barraRolagemTextArea ng-model=avaliacao.encerramento ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-encerramento\' | translate}}\"></textarea></md-input-container></div></div></v-pane-content></v-pane><v-pane expanded=true ng-if=\"identificacao.metodoAvaliacao != \'EVOLUTIVO\' && (listaBenfeitoriaAvaliacao.length > 0 || unidadeAutonomaIncorporacao)\"><v-pane-header class=\"textoAccordionPvgLaudo teste\">{{\'title-informar-valor-terreno-benfeitoria\' | translate}}</v-pane-header><v-pane-content><div layout=column flex><inserir-valores-terreno-benfeitoria ng-if=\"identificacao.metodoAvaliacao != \'EVOLUTIVO\' && (listaBenfeitoriaAvaliacao.length > 0 || unidadeAutonomaIncorporacao)\" lista-benfeitorias-imovel=listaBenfeitoriaAvaliacao moeda=avaliacao.resultadosAvaliacao.moeda unidade-autonoma-imovel=unidadeAutonomaIncorporacao tipo-valor-laudo=avaliacao.tipoValorLaudo tipo-laudo=LAUDO_RELATORIO validar-valor-imovel=validarValorImovel redirecionar-informar-valor-cub=redirecionarInformarValorCub redirecionar-informar-valor-edificacao=redirecionarInformarValorEdificacao total-valor-terreno=totalValorBenfeitorias valor-adotado-selecionado=valorAdotadoSelecionado rip-imovel=identificacao.rip valor-terreno=avaliacao.valorTerreno detalhar-laudo-relatorio=detalharLaudoRelatorio unidade-medida=unidadeMedida></inserir-valores-terreno-benfeitoria></div></v-pane-content></v-pane></v-accordion></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/resultadosAvaliacao.tmpl.html","<ng-form name=formularioValidacao><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header class=textoAccordionPvgLaudo>{{\'title-resultados-da-avaliacao\' | translate}}</v-pane-header><v-pane-content><div ng-if=\"laudoRelatorioAvaliacao.identificacao.metodologia == \'INFERENCIA_ESTATISTICA\'\"><div layout=column flex><div layout=row flex flex-xs><label class=textoAccordionPvgLaudo>{{\'title-valores\' | translate}}</label></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container class=md-block flex flex-xs flex-sm><label>{{\'title-moeda\' | translate}}:</label><md-select ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.resultadosAvaliacao.moeda name=\"{{nomeCampo}} - {{\'title-moeda\' | translate}}\" placeholder=\"{{\'title-moeda\' | translate}}\"><md-option ng-repeat=\"moeda in listaMoedas\" ng-value=moeda ng-selected=\"moeda.id==1\">{{moeda.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-valor-medio\' | translate}}:</label><input required ng-model=avaliacao.resultadosAvaliacao.valorMedio ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-valor-medio\' | translate}}\" ui-number-mask=2 ng-change=\"formatterValorMedio = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.valorMedio);calcularVariacaoValorAdotado(); calcularAmplitude();\" ng-blur=validarValores();calcularValorAdotadoInferencia();></md-input-container><label style=\"margin-top: 20px\" ng-bind=\"(avaliacao.resultadosAvaliacao.valorMedio ? formatterValorMedio : \'\')\"></label></div><div class=m-b-20 layout=row layout-xs=column ng-show=avaliacao.resultadosAvaliacao.valorMedio><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((avaliacao.resultadosAvaliacao.valorMedio/areaTotalTerreno) | currency).replace(\'R$\', \'\')}} {{avaliacao.resultadosAvaliacao.moeda ? avaliacao.resultadosAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-valor-minimo\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.resultadosAvaliacao.valorMinimo ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-valor-minimo\' | translate}}\" ui-number-mask=2 ng-change=\"formatterValorMinimo = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.valorMinimo); determinarValorAdotadoSelecionado();calcularAmplitude()\" ng-blur=validarValores();calcularValorAdotadoInferencia();></md-input-container><label ng-bind=\"(avaliacao.resultadosAvaliacao.valorMinimo ? formatterValorMinimo : \'\')\"></label></div><div class=m-b-20 layout=row layout-xs=column ng-show=avaliacao.resultadosAvaliacao.valorMinimo><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((avaliacao.resultadosAvaliacao.valorMinimo/areaTotalTerreno) | currency).replace(\'R$\', \'\')}} {{avaliacao.resultadosAvaliacao.moeda ? avaliacao.resultadosAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-valor-maximo\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.resultadosAvaliacao.valorMaximo ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-valor-maximo\' | translate}}\" ui-number-mask=2 ng-change=\"formatterValorMaximo = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.valorMaximo); determinarValorAdotadoSelecionado();calcularAmplitude()\" ng-blur=validarValores();calcularValorAdotadoInferencia();></md-input-container><label ng-bind=\"(avaliacao.resultadosAvaliacao.valorMaximo ? formatterValorMaximo : \'\')\"></label></div><div class=m-b-20 layout=row layout-xs=column ng-show=avaliacao.resultadosAvaliacao.valorMaximo><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((avaliacao.resultadosAvaliacao.valorMaximo/areaTotalTerreno) | currency).replace(\'R$\', \'\')}} {{avaliacao.resultadosAvaliacao.moeda ? avaliacao.resultadosAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout=row flex><md-input class=md-block flex flex-xs flex-sm layout=column><label class=\"sidenavTitulos deslocamentoTituloData font\">{{\'title-valor-adotado\' | translate}}:</label><md-radio-group ng-disabled=detalharLaudoRelatorio required flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=avaliacao.resultadosAvaliacao.tipoValor name=\"{{nomeCampo}} - {{\'title-valor-adotado\' | translate}}\" ng-change=calcularValorAdotadoInferencia();><div layout=row><md-radio-button ng-disabled=detalharLaudoRelatorio flex=30 flex-xs flex-sm flex-md class=\"tamanho-radio-button-avaliacao md-primary\" ng-repeat=\"tipoValor in listaTiposValores | orderBy: \'ordem\'\" ng-value=tipoValor.name><label class=texto-preto>{{tipoValor.descricao}}</label></md-radio-button></div></md-radio-group></md-input></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm ng-if=\"avaliacao.resultadosAvaliacao.tipoValor==OUTRO_VALOR\"><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-outro-valor-adotado\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio ng-model=avaliacao.resultadosAvaliacao.outroValorAdotado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-outro-valor-adotado\' | translate}}\" ui-number-mask=2 ng-change=\"formatterOutroValorAdotado = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.outroValorAdotado);calcularValorAdotadoInferencia();\" ng-required=\"avaliacao.resultadosAvaliacao.tipoValor==OUTRO_VALOR\"></md-input-container><label ng-bind=\"(avaliacao.resultadosAvaliacao.outroValorAdotado ? formatterOutroValorAdotado : \'\')\"></label></div><div class=m-b-20 layout=row layout-xs=column ng-show=\"avaliacao.resultadosAvaliacao.outroValorAdotado && avaliacao.resultadosAvaliacao.tipoValor==OUTRO_VALOR\"><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((avaliacao.resultadosAvaliacao.outroValorAdotado/areaTotalTerreno) | currency).replace(\'R$\', \'\')}} {{avaliacao.resultadosAvaliacao.moeda ? avaliacao.resultadosAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div><div layout-xs=row flex=50 flex-xs flex-sm ng-show=mostrarJustificativaValor><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>{{\'title-justificativa-do-valor-adotado\' | translate}}:</label><textarea ng-disabled=detalharLaudoRelatorio class=barraRolagemTextArea ng-model=avaliacao.resultadosAvaliacao.justificativaValorAdotado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-justificativa-do-valor-adotado\' | translate}}\" ng-required=\"avaliacao.resultadosAvaliacao.tipoValor!=VALOR_MEDIO && valorAdotadoSelecionado && (valorAdotadoSelecionado < variacaoInferiorValorMedio || valorAdotadoSelecionado > variacaoSuperiorValorMedio)\">\n                            </textarea></md-input-container></div><div layout=column class=m-t-20 flex><documento-laudo-relatorio-padrao hint=\"\'Devem ser anexados os resultados do modelo estatístico\'\" titulo=\"\'Relatórios Estatísticos\'\" detalhar-laudo-relatorio=detalharLaudoRelatorio lista-documentos-laudo=avaliacao.resultadosAvaliacao.relatoriosEstatisticos campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_02 tipo-anexo=\"\'Relatórios Estatísticos\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao></div><div layout=row layout-xs=column class=m-t-10><md-checkbox class=md-primary ng-model=avaliacao.resultadosAvaliacao.isArquivoModelo ng-change=limparDadosArquivosModelo(); name=\"{{\'label-incluir-arquivo-modelo\' | translate}}\" ng-disabled=detalharLaudoRelatorio><label class=text-black>{{\'label-incluir-arquivo-modelo\' | translate}}</label><md-tooltip md-direction=top>Adicionar arquivo do software utilizado para o cálculo</md-tooltip></md-checkbox></div><div layout=column flex ng-if=avaliacao.resultadosAvaliacao.isArquivoModelo><documento-laudo-relatorio-padrao hint=\"\'Adicionar arquivo do software utilizado para o cálculo\'\" titulo=\"\'Arquivos do Modelo\'\" lista-documentos-laudo=avaliacao.resultadosAvaliacao.arquivosModelo campo-data=\"\'true\'\" tipo-validacao=\"\'CONTENT_TYPE_03\'\" detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-relatorio-padrao></div></div></div><div ng-if=\"laudoRelatorioAvaliacao.identificacao.metodologia === \'OUTRO\'\"><div layout=column flex><div layout=row flex flex-xs><label class=textoAccordionPvgLaudo>{{\'title-valores\' | translate}}</label></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container class=md-block flex flex-xs flex-sm><label>{{\'title-moeda\' | translate}}:</label><md-select ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.resultadosAvaliacao.moeda name=\"{{nomeCampo}} - {{\'title-moeda\' | translate}}\" placeholder=\"{{\'title-moeda\' | translate}}\"><md-option ng-repeat=\"moeda in listaMoedas\" ng-value=moeda ng-selected=\"moeda.id==1\">{{moeda.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=row flex=50 flex-xs flex-sm><md-input-container layout-xs=row flex-xs flex=50 flex-sm class=md-block><label>{{\'title-valor-adotado\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio required ng-model=avaliacao.resultadosAvaliacao.valorAdotado ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-valor-adotado\' | translate}}\" ui-number-mask=2 ng-change=\"formatterValorAdotado = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.valorAdotado);\" ng-blur=validarValores();></md-input-container><label style=\"margin-top: 20px\" ng-bind=\"(avaliacao.resultadosAvaliacao.valorAdotado ? formatterValorMedio : \'\')\"></label></div></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex-xs flex=50 flex-sm class=md-block><label>{{\'title-valor-minimo\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio ng-model=avaliacao.resultadosAvaliacao.valorMinimo ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-valor-minimo\' | translate}}\" ui-number-mask=2 ng-change=\"formatterValorMinimo = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.valorMinimo); calcularAmplitude()\" ng-blur=validarValores()></md-input-container><label ng-bind=\"(avaliacao.resultadosAvaliacao.valorMinimo ? formatterValorMinimo : \'\')\"></label></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex-xs flex=50 flex-sm class=md-block><label>{{\'title-valor-maximo\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio ng-model=avaliacao.resultadosAvaliacao.valorMaximo ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-valor-maximo\' | translate}}\" ui-number-mask=2 ng-change=\"formatterValorMaximo = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.valorMaximo); calcularAmplitude()\" ng-blur=validarValores()></md-input-container><label ng-bind=\"(avaliacao.resultadosAvaliacao.valorMaximo ? formatterValorMaximo : \'\')\"></label></div><div layout=row layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex-xs flex=50 flex-sm class=md-block><label>{{\'title-valor-medio\' | translate}}:</label><input ng-disabled=\"detalharLaudoRelatorio || !avaliacao.resultadosAvaliacao.moeda\" ng-model=avaliacao.resultadosAvaliacao.valorMedio ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-valor-medio\' | translate}}\" ui-number-mask=2 ng-change=\"formatterValorMedio = convertNumeroPorExtenso(avaliacao.resultadosAvaliacao.valorMedio);calcularVariacaoValorAdotado(); calcularAmplitude();\" ng-blur=validarValores();></md-input-container><label style=\"margin-top: 20px\" ng-bind=\"(avaliacao.resultadosAvaliacao.valorMedio ? formatterValorMedio : \'\')\"></label></div><div class=m-b-20 layout=row layout-xs=column ng-show=avaliacao.resultadosAvaliacao.valorMedio><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -30px\"><span class=datepicker-label><span>m²: {{((avaliacao.resultadosAvaliacao.valorMedio/areaTotalTerreno) | currency).replace(\'R$\', \'\')}} {{avaliacao.resultadosAvaliacao.moeda ? avaliacao.resultadosAvaliacao.moeda.simbolo : listaMoedas[0].simbolo}}</span></span></div></div></div></v-pane-content></v-pane><div layout-xs=row flex=100 flex-xs flex-sm><md-input-container layout-xs=row flex flex-xs flex-sm class=md-block><label>Encerramento</label><textarea ng-disabled=detalharLaudoRelatorio class=barraRolagemTextArea ng-model=avaliacao.resultadosAvaliacao.encerramento ui-mask-placeholder-char=space> </textarea></md-input-container></div><v-pane expanded=true ng-if=\"laudoRelatorioAvaliacao.identificacao.tipo == \'RELATORIO\' && (listaBenfeitoriaAvaliacao.length > 0 || unidadeAutonomaIncorporacao)\"><v-pane-header class=\"textoAccordionPvgLaudo teste\">{{\'title-informar-valor-terreno-benfeitoria\' | translate}}</v-pane-header><v-pane-content><div layout=column flex><inserir-valores-terreno-benfeitoria ng-if=\"laudoRelatorioAvaliacao.identificacao.tipo == \'RELATORIO\' && (listaBenfeitoriaAvaliacao.length > 0 || unidadeAutonomaIncorporacao)\" lista-benfeitorias-imovel=listaBenfeitoriaAvaliacao moeda=avaliacao.resultadosAvaliacao.moeda unidade-autonoma-imovel=unidadeAutonomaIncorporacao tipo-valor-laudo=avaliacao.tipoValorLaudo tipo-laudo=LAUDO_RELATORIO validar-valor-imovel=validarValorImovel rip-imovel=identificacao.rip redirecionar-informar-valor-cub=redirecionarInformarValorCub redirecionar-informar-valor-edificacao=redirecionarInformarValorEdificacao total-valor-terreno=totalValorBenfeitorias valor-adotado-selecionado=valorAdotadoSelecionado valor-terreno=avaliacao.valorTerreno detalhar-laudo-relatorio=detalharLaudoRelatorio unidade-medida=unidadeMedida></inserir-valores-terreno-benfeitoria></div></v-pane-content></v-pane></v-accordion></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/terrenoImovel.tmpl.html","<ng-form name=formularioValidacao><md-content flex><div><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header>{{\'title-detalhes-terreno\' | translate}}</v-pane-header><v-pane-content><div layout=row layout-xs=column><div layout=column flex=100 flex-xs=100 flex-sm=100><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-memorial-descritivo\' | translate}}</label><input ng-model=terreno.memorialDescritivo name=\"{{\'label-memorial-descritivo\' | translate}}\" ng-disabled=isMemorialDescritivo></md-input-container></div><manter-edicao-campo is-campo=isMemorialDescritivo valor=terreno.memorialDescritivo ng-show=\"!isMemorialDescritivo && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isMemorialDescritivo ng-show=\"isMemorialDescritivo && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row><div layout=column flex=95 flex-xs=95 flex-sm=95><md-container layout=row flex=50 class=\"md-block m-b-20\"><div layout=column flex=50 flex-xs=50 flex-sm=50><md-radio-group id=radioIncra class=\"th-espacamento-radio-button tamanho-radio-button-avaliacao\" ng-model=terreno.unidadeMedida layout=row ng-disabled=isUnidadeMedidaArea><label class=espacamento-unidade>{{\'label-unidade-medida-area\' | translate}}:</label><br><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" ng-value=METRO_QUADRADO><label class=texto-preto>{{\'label-m2\' | translate}}</label></md-radio-button><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" ng-value=HECTARE><label class=texto-preto>{{\'label-ha\' | translate}}</label></md-radio-button></md-radio-group></div><manter-edicao-campo is-campo=isUnidadeMedidaArea valor=terreno.unidadeMedida ng-show=\"!isUnidadeMedidaArea && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isUnidadeMedidaArea ng-show=\"isUnidadeMedidaArea && !detalharLaudoRelatorio\"></habilitar-edicao-campo></md-container></div></div><div layout=row layout-xs=column><div layout=column flex=50 flex-xs=40 flex-sm=40><md-input-container class=md-block><label>{{\'label-area-total\' | translate}}</label><input ng-model=terreno.areaTotal name=\"{{\'label-area-total\' | translate}}\" ng-disabled=isAreaTotal ui-number-mask=4></md-input-container></div><manter-edicao-campo is-campo=isAreaTotal valor=terreno.areaTotal ng-show=\"!isAreaTotal && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isAreaTotal ng-show=\"isAreaTotal && !detalharLaudoRelatorio\"></habilitar-edicao-campo><div layout=column flex=50 flex-xs=40 flex-sm=40><md-input-container class=md-block><label>{{\'label-area-uniao\' | translate}}</label><input ng-model=terreno.areaUnial name=\"{{\'label-area-uniao\' | translate}}\" ng-disabled=isAreaUniao ui-number-mask=4></md-input-container></div><manter-edicao-campo is-campo=isAreaUniao valor=terreno.areaUnial ng-show=\"!isAreaUniao && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isAreaUniao ng-show=\"isAreaUniao && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row layout-xs=column><div layout=column flex=50 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-fracao-ideal-imovel\' | translate}}</label><input ng-model=terreno.fracaoIdealImovel name=\"{{\'label-fracao-ideal-imovel\' | translate}}\" ng-disabled=isFracaoIdealImovel ui-number-mask=7></md-input-container></div><manter-edicao-campo is-campo=isFracaoIdealImovel valor=terreno.fracaoIdealImovel ng-show=\"!isFracaoIdealImovel && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isFracaoIdealImovel ng-show=\"isFracaoIdealImovel && !detalharLaudoRelatorio\"></habilitar-edicao-campo><div layout=column flex=50 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-fracao-ideal-uniao\' | translate}}</label><input ng-model=terreno.fracaoIdealUniao name=\"{{\'label-fracao-ideal-uniao\' | translate}}\" ng-disabled=isFracaoIdealUniao ui-number-mask=7></md-input-container></div><manter-edicao-campo is-campo=isFracaoIdealUniao valor=terreno.fracaoIdealUniao ng-show=\"!isFracaoIdealUniao && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isFracaoIdealUniao ng-show=\"isFracaoIdealUniao && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row layout-xs=column><div layout=column flex=100 flex-xs=100 flex-sm=100><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-memorial-descritivo-area-uniao\' | translate}}</label><input ng-model=terreno.memorialDescritivoAreaUniao name=\"{{\'label-fracao-ideal-uniao\' | translate}}\" ng-disabled=isMemorialDescritivoAreaUniao></md-input-container></div><manter-edicao-campo is-campo=isMemorialDescritivoAreaUniao valor=terreno.memorialDescritivoAreaUniao ng-show=\"!isMemorialDescritivoAreaUniao && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isMemorialDescritivoAreaUniao ng-show=\"isMemorialDescritivoAreaUniao && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div class=upload-arquivos><div layout=row flex layout-align=\"start start\"><h3 class=subtitulo-pagina>{{\'title-restricao-uso-ocupacao\' | translate}}</h3></div><div layout=row layout-xs=column flex-sm=column><md-container class=md-block flex flex-xs flex-sm><div layout=row flex><div flex layout=column flex-offset=5><md-checkbox ng-disabled=true class=md-primary ng-model=terreno.isZonaInteresseSocial name=\"{{\'label-zona-area-interesse-social\' | translate}}\"><label class=text-black>{{\'label-zona-area-interesse-social\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=true class=md-primary ng-model=terreno.isSituadoAreaSeguranca name=\"{{\'label-situado-em-area-seguranca\' | translate}}\"><label class=text-black>{{\'label-situado-em-area-seguranca\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=true class=md-primary ng-model=terreno.isAreaNonAedificandi name=\"{{\'label-area-non-aedificandi\' | translate}}\"><label class=text-black>{{\'label-area-non-aedificandi\' | translate}}</label></md-checkbox><md-checkbox ng-disabled=true class=\"md-primary with-field\" ng-model=terreno.isTombadoPatrimonioHistorico><label class=text-black><span>{{\'label-tombado-como-patrimonio-historico\' | translate}}</span><md-select ng-model=terreno.tipoPatrimonioHistorico disabled><md-option ng-repeat=\"tipoPatrimonioHistorico in listaTiposImovel\" ng-value=tipoPatrimonioHistorico ng-selected=\"terreno.tipoPatrimonioHistorico.id == tipoPatrimonioHistorico.id\">{{tipoPatrimonioHistorico.descricao}}</md-option></md-select></label></md-checkbox><md-checkbox ng-disabled=true class=\"md-primary with-field\" ng-model=terreno.isOutraRestricaoUso><label class=text-black><span>{{\'label-outra-restricao-uso-ocupacao\' | translate}}</span><md-input-container class=\"input-table md-block\" flex=100 flex-xs=100 flex-sm=100><input ng-disabled=true ng-model=imovel.outraRestricaoUso disabled></md-input-container></label></md-checkbox></div></div></md-container></div></div><documento-laudo-relatorio-padrao lista-documentos-laudo=terreno.arquivosComplementares titulo=\"\'Arquivos Complementares\'\" campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_01 detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-relatorio-padrao><div layout=row flex><h3>{{\'title-descricao-caracteristicas-fisica-terreno\' | translate}}</h3></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-dimencao\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.dimensoes name=\"{{\'label-dimencao\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-nivel-logradouro\' | translate}}<md-tooltip md-direction=top>Mesmo nível, acima ou abaixo do logradouro</md-tooltip></label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.nivelRua name=\"{{\'label-nivel-logradouro\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-formato\' | translate}}<md-tooltip md-direction=top>retangular, trapezoidal, triangular etc.</md-tooltip></label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.formato name=\"{{\'label-formato\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-posicao\' | translate}}<md-tooltip md-direction=top>meio de quadra, encravado, esquina, mais de uma testada</md-tooltip></label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.posicao name=\"{{\'label-posicao\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-topografia\' | translate}}<md-tooltip md-direction=top>plano, inclinação suave, inclinação forte, acidentado etc.</md-tooltip></label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.topografia name=\"{{\'label-topografia\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-situacao\' | translate}}<md-tooltip md-direction=top>\"seco\", \"alagado\" e \"inundável\".</md-tooltip></label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.situacao name=\"{{\'label-situacao\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-observacao-complementacao\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.observacoesCaracteristicas name=\"{{\'label-observacao-complementacao\' | translate}}\"></textarea></md-input-container></div></v-pane-content></v-pane></v-accordion></div><div><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header style=\"margin-top:15px !important\" class=textoAccordionPvgLaudo>{{\'title-parametros-urbanisticos\' | translate}}</v-pane-header><v-pane-content><documento-laudo-relatorio-padrao lista-documentos-laudo=documentosParametrosUrbanisticos titulo=\"\'Documentos\'\" campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_01 detalhar-laudo-avaliacao=detalharLaudoRelatorio tipo-anexo=\"\'Documento Parâmetros Urbanísticos\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></documento-laudo-relatorio-padrao><div layout=row layout-xs=column class=m-t-20><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-zoneamento\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.zoneamento name=\"{{\'label-zoneamento\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-afastamento-lateral\' | translate}}</label><input ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.afastamentoLateral name=\"{{\'label-afastamento-lateral\' | translate}}\" ui-number-mask=2></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label class=concerta-label>{{\'label-afastamento-frontal\' | translate}}</label><input ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.afastamentoFrontal name=\"{{\'label-afastamento-frontal\' | translate}}\" ui-number-mask=2></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-gabarito\' | translate}}</label><input ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.gabarito name=\"{{\'label-gabarito\' | translate}}\" ui-number-mask=2></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label class=concerta-label>{{\'label-taxa-ocupacao-maxima\' | translate}}</label><input ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.taxaOcupacaoMaxima name=\"{{\'label-taxa-ocupacao-maxima\' | translate}}\" ui-number-mask=2></md-input-container><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label class=concerta-label>{{\'label-indice-aproveitamento\' | translate}}</label><input ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.indiceAproveitamento name=\"{{\'label-indice-aproveitamento\' | translate}}\" ui-number-mask=2></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-observacao\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=terrenoImovel.observacoesParametrosUrbanisticos name=\"{{\'label-observacao\' | translate}}\"></textarea></md-input-container></div></v-pane-content></v-pane></v-accordion></div></md-content></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/unidadeAutonoma.tmpl.html","<ng-form name=formularioValidacao><md-content flex><div><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header>{{\'title-dados-edificacao\' | translate}}</v-pane-header><v-pane-content><div layout=row layout-xs=column><div layout=column flex=50 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-memorial-descritivo\' | translate}}</label><md-select ng-model=unidadeAutonomaIncorporacao.edificacao.tipoEdificacao name=\"{{\'label-memorial-descritivo\' | translate}}\" ng-disabled=\"isTipoEdificacao || detalharLaudoRelatorio\"><md-option ng-repeat=\"tipoEdificacao in listaTiposEdificacao\" ng-value=tipoEdificacao ng-selected=\"unidadeAutonomaIncorporacao.edificacao.tipoEdificacao.id==tipoEdificacao.id\">{{tipoEdificacao.descricao}}</md-option></md-select></md-input-container></div><manter-edicao-campo is-campo=isTipoEdificacao valor=unidadeAutonomaIncorporacao.edificacao.tipoEdificacao ng-show=\"!isTipoEdificacao && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isTipoEdificacao ng-show=\"isTipoEdificacao && !detalharLaudoRelatorio\"></habilitar-edicao-campo><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><div layout=row flex=100><div layout=column flex=100 flex-xs=100 flex-sm=100 flex-offset=5><md-checkbox class=md-primary ng-model=unidadeAutonomaIncorporacao.edificacao.possuiElevador name=\"{{\'label-possui-elevador\' | translate}}\" ng-disabled=\"isPossuiElevador || detalharLaudoRelatorio\">{{\'label-possui-elevador\' | translate}}</md-checkbox></div><manter-edicao-campo is-campo=isPossuiElevador valor=unidadeAutonomaIncorporacao.edificacao.possuiElevador ng-show=\"!isPossuiElevador && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isPossuiElevador ng-show=\"isPossuiElevador && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div></md-input-container></div><div layout=row layout-xs=column><div layout=column flex=50 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-tipo-projeto\' | translate}}</label><md-select ng-model=unidadeAutonomaIncorporacao.edificacao.tipoProjeto name=\"{{\'label-tipo-projeto\' | translate}}\" ng-disabled=\"isTipoProjeto || detalharLaudoRelatorio\"><md-option ng-repeat=\"tipoProjeto in listaTiposProjeto\" ng-value=tipoProjeto ng-selected=\"unidadeAutonomaIncorporacao.edificacao.tipoProjeto.id==tipoProjeto.id\">{{tipoProjeto.descricao}}</md-option></md-select></md-input-container></div><manter-edicao-campo is-campo=isTipoProjeto valor=unidadeAutonomaIncorporacao.edificacao.tipoProjeto ng-show=\"!isTipoProjeto && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isTipoProjeto ng-show=\"isTipoProjeto && !detalharLaudoRelatorio\"></habilitar-edicao-campo><div layout=column flex=50 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-nome\' | translate}}</label><input ng-model=unidadeAutonomaIncorporacao.edificacao.nome name=\"{{\'label-nome\' | translate}}\" ng-disabled=isNomeEdificacao></md-input-container></div><manter-edicao-campo is-campo=isNomeEdificacao valor=unidadeAutonomaIncorporacao.edificacao.nome ng-show=\"!isNomeEdificacao && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isNomeEdificacao ng-show=\"isNomeEdificacao && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row layout-xs=column><div layout=column flex=100 flex-xs=100 flex-sm=100><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-observacao-gerais-edificacao\' | translate}}</label><textarea class=barraRolagemTextArea rows=1 cols=50 maxlength=2000 name=\"{{\'label-observacao-gerais-edificacao\' | translate}}\" ng-model=unidadeAutonomaIncorporacao.edificacao.observacoes ng-disabled=\"isObservacoesGeraisEdificacao || detalharLaudoRelatorio\">\n                                    </textarea><span style=\"color: #cc0000; float: right\">{{0 + unidadeAutonomaIncorporacao.edificacao.observacoes.length}}/2000</span></md-input-container></div><manter-edicao-campo is-campo=isObservacoesGeraisEdificacao valor=unidadeAutonomaIncorporacao.edificacao.observacoes ng-show=\"!isObservacoesGeraisEdificacao && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isObservacoesGeraisEdificacao ng-show=\"isObservacoesGeraisEdificacao && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><documento-edificacao-unidade-autonoma lista-documentos-edificacao-unidade-autonoma=unidadeAutonomaIncorporacao.edificacao.documentacao redirecionar-cadastrar-documento-edificacao-unidade-autonoma=redirecionarCadastrarDocumentoEdificacaoUnidadeAutonoma titulo=\"\'Documentação\'\" custom-nome-label=\"\'Nome do Documento\'\" detalhar-laudo-relatorio=detalharLaudoRelatorio custom-button-label=\"\'Selecionar\'\" campo-data=\"\'true\'\"></documento-edificacao-unidade-autonoma></v-pane-content></v-pane></v-accordion><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header>{{\'title-condicoes-acessibilidade\' | translate}}</v-pane-header><v-pane-content><div layout=row layout-xs=column class=m-t-20><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><div style=\"padding-left: 3px\"><label class=new-label-large>{{\'label-vagas-deficientes-acessiveis\' | translate}}</label></div><md-select ng-model=unidadeAutonomaIncorporacao.condicoesAcessibilidade.vaga ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"item in condicoes\" ng-value=item.nome ng-selected=\"item.nome == unidadeAutonomaIncorporacao.condicoesAcessibilidade.vaga\">{{item.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><div style=\"padding-left: 3px\"><label class=new-label-large>{{\'label-acessos-interior-acessiveis\' | translate}}</label></div><md-select ng-model=unidadeAutonomaIncorporacao.condicoesAcessibilidade.mobilidade ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"item in condicoes\" ng-value=item.nome ng-selected=\"item.nome == unidadeAutonomaIncorporacao.condicoesAcessibilidade.mobilidade\">{{item.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><div style=\"padding-left: 3px\"><label class=new-label-large>{{\'label-banheiro-acessivel\' | translate}}</label></div><md-select ng-model=unidadeAutonomaIncorporacao.condicoesAcessibilidade.banheiro ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"item in condicoes\" ng-value=item.nome ng-selected=\"item.nome == unidadeAutonomaIncorporacao.condicoesAcessibilidade.banheiro\">{{item.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><div style=\"padding-left: 3px\"><label class=new-label-large>{{\'label-elevador-acessivel\' | translate}}</label></div><md-select ng-model=unidadeAutonomaIncorporacao.condicoesAcessibilidade.elevador ng-disabled=detalharLaudoRelatorio><md-option ng-repeat=\"item in condicoes\" ng-value=item.nome ng-selected=\"item.nome == unidadeAutonomaIncorporacao.condicoesAcessibilidade.elevador\">{{item.nome}}</md-option></md-select></md-input-container></div></v-pane-content></v-pane></v-accordion><v-accordion multiple class=vAccordion--default><v-pane expanded=true><v-pane-header>{{\'title-detalhes-unidade-autonoma\' | translate}}</v-pane-header><v-pane-content><div layout=row layout-xs=column><div layout=column flex=50 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-area-contruida\' | translate}}</label><input ng-model=unidadeAutonomaIncorporacao.area name=\"{{\'label-area-contruida\' | translate}}\" ng-disabled=\"isAreaConstruida || detalharLaudoRelatorio\" ui-number-mask=2></md-input-container></div><manter-edicao-campo is-campo=isAreaConstruida valor=unidadeAutonomaIncorporacao.area ng-show=\"!isAreaConstruida && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isAreaConstruida ng-show=\"isAreaConstruida && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row layout-xs=column><div layout=column flex=100 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-memorial-descritivo\' | translate}}</label><input ng-model=unidadeAutonomaIncorporacao.memorialDescritivo name=\"{{\'label-memorial-descritivo\' | translate}}\" ng-disabled=\"isMemorialDescritivo || detalharLaudoRelatorio\"></md-input-container></div><manter-edicao-campo is-campo=isMemorialDescritivo valor=unidadeAutonomaIncorporacao.memorialDescritivo ng-show=\"!isMemorialDescritivo && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isMemorialDescritivo ng-show=\"isMemorialDescritivo && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row layout-xs=column><div layout=column flex=100 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-observacao-gerais-unidade-autonoma\' | translate}}</label><textarea class=barraRolagemTextArea rows=1 cols=50 maxlength=2000 name=\"{{\'label-observacao-gerais-unidade-autonoma\' | translate}}\" ng-model=unidadeAutonomaIncorporacao.observacoes ng-disabled=\"isObservacoesGeraisUnidadeAutonoma || detalharLaudoRelatorio\"></textarea><span style=\"color: #cc0000; float: right\">{{0 + unidadeAutonomaIncorporacao.observacoes.length}}/2000</span></md-input-container></div><manter-edicao-campo is-campo=isObservacoesGeraisUnidadeAutonoma valor=unidadeAutonomaIncorporacao.observacoes ng-show=\"!isObservacoesGeraisUnidadeAutonoma && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isObservacoesGeraisUnidadeAutonoma ng-show=\"isObservacoesGeraisUnidadeAutonoma && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><div layout=row layout-xs=column><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-numero-pavimento\' | translate}}</label><input ng-disabled=detalharLaudoRelatorio ng-model=unidadeAutonoma.numeroPavimentos name=\"{{\'label-numero-pavimento\' | translate}}\" type=number></md-input-container><div layout=column flex=33 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-vaga-garagem-coberta\' | translate}}</label><input ng-model=unidadeAutonomaIncorporacao.numeroVagasCobertas name=\"{{\'label-vaga-garagem-coberta\' | translate}}\" type=number ng-disabled=\"isVagasGaragemCobertas || detalharLaudoRelatorio\"></md-input-container></div><manter-edicao-campo is-campo=isVagasGaragemCobertas valor=unidadeAutonomaIncorporacao.numeroVagasCobertas ng-show=\"!isVagasGaragemCobertas && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isVagasGaragemCobertas ng-show=\"isVagasGaragemCobertas && !detalharLaudoRelatorio\"></habilitar-edicao-campo><div layout=column flex=33 flex-xs=100 flex-sm=100><md-input-container class=md-block><label>{{\'label-vaga-garagem-descoberta\' | translate}}</label><input ng-model=unidadeAutonomaIncorporacao.numeroVagasDescobertas name=\"{{\'label-vaga-garagem-descoberta\' | translate}}\" type=number ng-disabled=\"isVagasGaragemDescobertas || detalharLaudoRelatorio\"></md-input-container></div><manter-edicao-campo is-campo=isVagasGaragemDescobertas valor=unidadeAutonomaIncorporacao.numeroVagasDescobertas ng-show=\"!isVagasGaragemDescobertas && !detalharLaudoRelatorio\"></manter-edicao-campo><habilitar-edicao-campo is-campo=isVagasGaragemDescobertas ng-show=\"isVagasGaragemDescobertas && !detalharLaudoRelatorio\"></habilitar-edicao-campo></div><documento-laudo-relatorio-padrao lista-documentos-laudo=unidadeAutonomaIncorporacao.arquivosPlanta titulo=\"\'Arquivos de Planta\'\" campo-data=\"\'true\'\" tipo-validacao=CONTENT_TYPE_01 detalhar-laudo-avaliacao=detalharLaudoRelatorio></documento-laudo-relatorio-padrao><div layout=row flex><h3>{{\'title-descricao-unidade-autonoma\' | translate}}</h3></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-divisao-interna\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=unidadeAutonoma.divisaoInterna name=\"{{\'label-divisao-interna\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-material-construtivo-interno\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=unidadeAutonoma.materiaisConstrutivosInternos name=\"{{\'label-material-construtivo-interno\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-estrutura\' | translate}}<md-tooltip md-direction=top>estrutura convencional de concreto armado, estrutura de concreto protendido, estrutura metálica, estrutura de madeira, alvenaria estrutural, mista etc.</md-tooltip></label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=unidadeAutonoma.estrutura name=\"{{\'label-estrutura\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-cobertura\' | translate}}<md-tooltip md-direction=top>laje plana, telhas cerâmicas, telhas metálicas, telhas de fibrocimento etc.</md-tooltip></label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=unidadeAutonoma.cobertura name=\"{{\'label-cobertura\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-fachada\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=unidadeAutonoma.fachada name=\"{{\'label-fachada\' | translate}}\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-instalação-equipamento\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=unidadeAutonoma.instalacoesEquipamentos name=\"{{\'label-instalação-equipamento\' | translate}}\"></textarea></md-input-container></div></v-pane-content></v-pane></v-accordion></div></md-content></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/laudo-relatorio-avaliacao/templates/vistoria.tmpl.html","<ng-form name=formularioValidacao><div id=formData layout=column><div layout=row flex><h3>{{\'title-dados-vistoria\' | translate}}</h3></div><div layout=row layout-xs=column ng-show=\"vistoria.execucaoVistoria == \'VISTORIA_REALIZADA_EM_UM_DIA\'\"><div class=md-block flex-gt-xs flex=50></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'tr-data-vistoria\' | translate}}: *</span></span></div></div><div layout=row layout-xs=column flex=100 flex-xs=100><md-input-container class=md-block flex=50 flex-xs=100 flex-sm=100><label>{{\'label-execucao-vistoria\' | translate}}:</label><md-select ng-disabled=detalharLaudoRelatorio ng-model=vistoria.execucaoVistoria required ng-change=selecionarVistoriaImovel() name=\"{{nomeCampo}} - {{\'label-execucao-vistoria\' | translate}}\" placeholder=\"{{\'label-execucao-vistoria\' | translate}}\"><md-option ng-repeat=\"(k,v) in listaExecucaoVistoria\" ng-value=k>{{v}}</md-option></md-select></md-input-container><div flex=50 layout=column ng-show=\"vistoria.execucaoVistoria == \'VISTORIA_REALIZADA_EM_UM_DIA\'\"><md-input class=md-block><md-datepicker md-max-date=dataAtual ng-disabled=detalharLaudoRelatorio flex-xs=100 flex-sm flex-md ng-model=vistoria.dataVistoria name=\"\'DataVistoria\' - {{\'label-execucao-vistoria\' | translate}}\" md-placeholder=\"{{\'tr-data-vistoria\' | translate}}\" ng-required=\"vistoria.execucaoVistoria == \'VISTORIA_REALIZADA_EM_UM_DIA\'\" ng-change=validarDataVistoria();></md-datepicker></md-input></div></div><div class=m-t-10 layout=row layout-xs=column flex-sm=column layout-align-sm=\"start start\" ng-if=\"vistoria.execucaoVistoria != null && vistoria.execucaoVistoria != \'VISTORIA_NAO_REALIZADA\'\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-anotacoes-observacoes-vistoria\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=vistoria.observacao></textarea></md-input-container></div><div class=m-t-10 layout=row layout-xs=column flex-sm=column layout-align-sm=\"start start\" ng-if=\"vistoria.execucaoVistoria == \'VISTORIA_NAO_REALIZADA\'\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-justificativa\' | translate}}</label><textarea class=barraRolagemTextArea ng-disabled=detalharLaudoRelatorio ng-model=vistoria.justificativa maxlength=2000 ng-required=\"vistoria.execucaoVistoria == \'VISTORIA_NAO_REALIZADA\' && identificacao.tipo == \'LAUDO\'\"></textarea><span style=\"color: #cc0000; float: right\">{{0 + vistoria.justificativa.length}}/2000</span></md-input-container></div></div><div layout=row layout-xs=column flex=100 flex-xs=100><div ng-if=\"vistoria.dataVistoria && (vistoria.execucaoVistoria != \'VISTORIA_NAO_REALIZADA\')\" flex=100 flex-xs=100 flex-sm=100><div layout=row flex><h3>{{\'title-fotos-vistoria-imovel\' | translate}}</h3></div><label>{{\'tr-selecionar-item\' | translate}}</label><accordeon-arquivo-vistoria detalhar-laudo-relatorio=detalharLaudoRelatorio is-vistoria=vistoria.isNaoPossuiVistoria lista-fotos-imovel=vistoriaListaFotosImovel lista-fotos-benfeitoria=vistoriaListaFotosBenfeitorias lista-benfeitoria=listaBenfeitoria lista-fotos-unidade-autonoma=vistoriaListaFotosUnidadeAutonoma unidade-autonoma=unidadeAutonoma anexo-laudo-relatorio=anexoLaudoRelatorio></accordeon-arquivo-vistoria></div><div ng-if=\"(vistoria.execucaoVistoria == \'VISTORIA_NAO_REALIZADA\' && identificacao.tipo == \'RELATORIO\') && (listaFotosImovel.length > 0 || listaFotosBenfeitorias.length > 0 || listaFotosUnidadeAutonoma.length > 0)\" flex=100 flex-xs=100 flex-sm=100><div layout=row flex><h3>{{\'title-fotos-do-imovel\' | translate}}</h3></div><label>{{\'tr-selecionar-item-fotos\' | translate}}</label><accordeon-arquivo-vistoria detalhar-laudo-relatorio=detalharLaudoRelatorio is-vistoria=vistoria.isNaoPossuiVistoria lista-fotos-imovel=listaFotosImovel lista-fotos-benfeitoria=listaFotosBenfeitorias lista-benfeitoria=listaBenfeitoria anexo-laudo-relatorio=anexoLaudoRelatorio lista-fotos-unidade-autonoma=listaFotosUnidadeAutonoma unidade-autonoma=unidadeAutonoma></accordeon-arquivo-vistoria></div></div></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarAvaliacaoPvgIncra.html","<div layout=row><md-button ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\" ng-click=cadastrarPVGIncra() id=dialogAvaliacaoIncra class=\"btn-size-big btn-avaliacao\">{{\'button-incluir-avaliacao\' | translate}}</md-button></div><div flex class=caixa-tabela-resultado><div layout=column ng-show=\"pvgsIncra.length > 0\"><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th class=th-margin-pvgs md-column>{{\'tr-situacao\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-periodo-avaliacao\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-valor-unitario\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-area-terreno-fracao\' | translate}} (m²)</th><th class=th-margin-pvgs md-column>{{\'tr-valor-do-terreno\' | translate}} (R$)</th><th class=th-margin-pvgs md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"pvgI in pvgsIncra\"><td class=th-margin-pvgs md-cell ng-show=\"pvgI.status != \'PENDENTE_VALIDACAO_TECNICO\' && pvgI.status != \'PENDENTE_VALIDACAO_CHEFIA\'\">{{listaStatusAvaliacao[pvgI.status]}}</td><td class=th-margin-pvgs md-cell ng-show=\"pvgI.status == \'PENDENTE_VALIDACAO_TECNICO\' || pvgI.status == \'PENDENTE_VALIDACAO_CHEFIA\'\"><a ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'true\' || usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'true\' || usuarioLogado.AVALIACAO_ACOES_TECNICO == \'true\'\" href=\"\" ng-click=redirecionarConsultarAcoesAvaliacao() target=_blank>{{listaStatusAvaliacao[pvgI.status]}}</a><label ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'false\' && usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'false\' && usuarioLogado.AVALIACAO_ACOES_TECNICO == \'false\'\">{{listaStatusAvaliacao[pvgI.status]}}</label></td><td class=th-margin-pvgs md-cell ng-bind=\"(pvgI.dataInicial | date: \'dd/MM/yyyy\' : \'-0300\') + (pvgI.dataFinal ? \' a \' : \'\') + (pvgI.dataFinal | date: \'dd/MM/yyyy\' : \'-0300\')\"></td><td class=th-margin-pvgs-centralizada md-cell>{{(pvgI.valor | currency).replace(\'R$\', \'\')+ \' R$/\' + (pvgI.tipoUnidade == \'M2\' ? \'m²\' : \'ha\' )}}</td><td class=th-margin-pvgs-centralizada md-cell>{{(pvgI.areaTerreno | currency).replace(\'R$\', \'\')}}</td><td class=th-margin-pvgs-centralizada md-cell>{{(pvgI.valorTerreno | currency).replace(\'R$\', \'\')}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=detalharPVGIncra(pvgI.id)><md-icon ng-bind=\"\'search\'\"><md-tooltip>{{\'label-detalhar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\" class=\"md-fab md-raised md-mini\" ng-show=\"pvgI.status==\'ATIVO\' || pvgI.status==\'VIGENTE\'\" ng-click=cancelarPvgIncra(pvgI.id);><md-icon ng-bind=\"\'clear\'\"><md-tooltip>{{\'label-cancelar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\" class=\"md-fab md-raised md-mini\" ng-show=\"pvgI.status==\'PENDENTE_VALIDACAO_TECNICO\' || pvgI.status==\'PENDENTE_VALIDACAO_CHEFIA\'\" ng-click=excluirPvgIncra(pvgI.id);><md-icon ng-bind=\"\'delete\'\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=buscarPvgsIncra md-page-select></md-table-pagination></md-conent></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarAvaliacaoPvgMunicipio.html","<div layout=row><md-button ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\" ng-click=cadastrarPVGMunicipio() class=\"btn-size-big-big btn-avaliacao\">{{\'button-incluir-avaliacao\' | translate}}</md-button></div><!-- Listagem de Avaliações Pvg Municipio --><div flex class=caixa-tabela-resultado><div layout=column ng-show=\"pvgsMunicipio.length > 0\"><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th class=th-margin-pvgs md-column>{{\'tr-situacao\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-periodo-avaliacao\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-valor\' | translate}} m² (R$)</th><th class=th-margin-pvgs md-column>{{\'tr-area-terreno-fracao\' | translate}} (m²)</th><th class=th-margin-pvgs md-column>{{\'tr-valor-do-terreno\' | translate}} (R$)</th><th class=th-margin-pvgs md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"pvgM in pvgsMunicipio\"><td class=th-margin-pvgs md-cell ng-show=\"pvgM.status != \'PENDENTE_VALIDACAO_TECNICO\' && pvgM.status != \'PENDENTE_VALIDACAO_CHEFIA\'\">{{listaStatusAvaliacao[pvgM.status]}}</td><td class=th-margin-pvgs md-cell ng-show=\"pvgM.status == \'PENDENTE_VALIDACAO_TECNICO\' || pvgM.status == \'PENDENTE_VALIDACAO_CHEFIA\'\"><a ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'true\' || usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'true\' || usuarioLogado.AVALIACAO_ACOES_TECNICO == \'true\'\" href=\"\" ng-click=redirecionarConsultarAcoesAvaliacao() target=_blank>{{listaStatusAvaliacao[pvgM.status]}}</a><label ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'false\' && usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'false\' && usuarioLogado.AVALIACAO_ACOES_TECNICO == \'false\'\">{{listaStatusAvaliacao[pvgM.status]}}</label></td><td class=th-margin-pvgs md-cell ng-bind=\"(pvgM.dataInicial | date: \'dd/MM/yyyy\' : \'-0300\') + (pvgM.dataFinal ? \' a \' : \'\') + (pvgM.dataFinal | date: \'dd/MM/yyyy\' : \'-0300\')\"></td><td class=th-margin-pvgs-centralizada md-cell>{{(pvgM.valor | currency).replace(\'R$\', \'\')}}</td><td class=th-margin-pvgs-centralizada md-cell>{{(pvgM.areaTerreno | currency).replace(\'R$\', \'\')}}</td><td class=th-margin-pvgs-centralizada md-cell>{{(pvgM.valorTerreno | currency).replace(\'R$\', \'\')}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=detalharPVGMunicipio(pvgM.id)><md-icon ng-bind=\"\'search\'\"><md-tooltip>{{\'label-detalhar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\" class=\"md-fab md-raised md-mini\" ng-show=\"pvgM.status==\'ATIVO\' || pvgM.status==\'VIGENTE\'\" ng-click=cancelarPvgMunicipio(pvgM.id)><md-icon ng-bind=\"\'clear\'\"><md-tooltip>{{\'label-cancelar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\" class=\"md-fab md-raised md-mini\" ng-show=\"pvgM.status==\'PENDENTE_VALIDACAO_TECNICO\' || pvgM.status==\'PENDENTE_VALIDACAO_CHEFIA\'\" ng-click=excluirPvgMunicipio(pvgM.id);><md-icon ng-bind=\"\'delete\'\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=buscarPvgsMunicipio md-page-select></md-table-pagination></md-conent></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarAvaliacaoPvgSpu.html","<div ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(imovel.enderecamento.uf) != -1)\" layout=row layout-xs=column><md-button class=\"btn-size-big btn-avaliacao\" ng-click=cadastrarPVGSpu() flex-offset=10 ng-show=!showAvaliacoesTerrenos>{{\'label-selecionar-trecho\' | translate}}</md-button><md-button class=\"btn-size-big btn-avaliacao\" ng-click=cadastrarPVGSpu() flex-offset=10 ng-show=showAvaliacoesTerrenos>{{\'label-alterar-trecho\' | translate}}</md-button><md-button class=\"btn-size-big btn-avaliacao\" ng-click=desvincularPVGSpu() flex-offset=10 ng-show=showDesvincularPvgSpu>{{\'label-desvincular-trecho\' | translate}}</md-button></div><!-- Listagem de Avaliações Pvg Municipio --><div flex class=caixa-tabela-resultado><div layout=column ng-show=\"historicoAvaliacoesPvgsSpu.length > 0\"><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'tr-situacao\' | translate}}</th><th style=\"width: 15%\" class=th-margin-pvgs md-column>{{\'tr-periodo-avaliacao\' | translate}}</th><th style=\"width: 15%\" class=th-margin-pvgs md-column>{{\'label-trecho\' | translate}}</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'tr-fator-corretivo-total\' | translate}}</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'tr-valor\' | translate}} m² (R$)</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'tr-area-terreno-fracao\' | translate}} (m²)</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'tr-valor-do-terreno\' | translate}} (R$)</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"avaliacaoPvgSpu in historicoAvaliacoesPvgsSpu\"><td class=th-margin-pvgs md-cell ng-if=\"avaliacaoPvgSpu.statusAvaliacao != \'PENDENTE_VALIDACAO_TECNICO\' && avaliacaoPvgSpu.statusAvaliacao != \'PENDENTE_VALIDACAO_CHEFIA\'\">{{listaStatusAvaliacao[avaliacaoPvgSpu.statusAvaliacao]}}</td><td class=th-margin-pvgs md-cell ng-show=\"avaliacaoPvgSpu.statusAvaliacao == \'PENDENTE_VALIDACAO_TECNICO\' || avaliacaoPvgSpu.statusAvaliacao == \'PENDENTE_VALIDACAO_CHEFIA\'\"><a ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'true\' || usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'true\' || usuarioLogado.AVALIACAO_ACOES_TECNICO == \'true\'\" href=\"\" ng-click=redirecionarConsultarAcoesAvaliacao() target=_blank>{{listaStatusAvaliacao[avaliacaoPvgSpu.statusAvaliacao]}}</a><label ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'false\' && usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'false\' && usuarioLogado.AVALIACAO_ACOES_TECNICO == \'false\'\">{{listaStatusAvaliacao[avaliacaoPvgSpu.statusAvaliacao]}}</label></td><td class=th-margin-pvgs md-cell ng-bind=\"(avaliacaoPvgSpu.dataInicioPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') + (avaliacaoPvgSpu.dataFinalPeriodo ? \' a \' + (avaliacaoPvgSpu.dataFinalPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') : \'\')\"></td><td class=th-margin-pvgs md-cell>{{avaliacaoPvgSpu.trecho.descricao}}</td><td class=th-margin-pvgs md-cell>{{(avaliacaoPvgSpu.fatorCorretivoTotal | currency:\'\':3)}}</td><td class=th-margin-pvgs-centralizada style=\"width: 10%\" md-cell>{{(avaliacaoPvgSpu.valorAvaliacao | currency).replace(\'R$\', \'\')}}</td><td class=th-margin-pvgs-centralizada style=\"width: 10%\" md-cell>{{(avaliacaoPvgSpu.areaFracaoTerreno | currency).replace(\'R$\', \'\')}}</td><td class=th-margin-pvgs-centralizada md-cell>{{(avaliacaoPvgSpu.valorTerreno | currency).replace(\'R$\', \'\')}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=detalharPVGSpu(avaliacaoPvgSpu.id)><md-icon ng-bind=\"\'search\'\"><md-tooltip>{{\'label-detalhar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(imovel.enderecamento.uf) != -1)\" class=\"md-fab md-raised md-mini\" ng-click=editarFatores(avaliacaoPvgSpu.id) ng-show=\"avaliacaoPvgSpu.statusAvaliacao == \'VIGENTE\' || avaliacaoPvgSpu.statusAvaliacao == \'ATIVO\'\"><md-icon ng-bind=\"\'edit\'\"><md-tooltip>{{\'label-editar-fatores\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(imovel.enderecamento.uf) != -1)\" class=\"md-fab md-raised md-mini\" ng-show=\"avaliacaoPvgSpu.statusAvaliacao==\'PENDENTE_VALIDACAO_TECNICO\' || avaliacaoPvgSpu.statusAvaliacao==\'PENDENTE_VALIDACAO_CHEFIA\'\" ng-click=excluirPvgSpu(avaliacaoPvgSpu.id);><md-icon ng-bind=\"\'delete\'\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=buscarHistoricoPvgSpu md-page-select></md-table-pagination></md-content></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarLaudoAvaliacao.html","<div layout=row><md-button ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\" ng-click=redirecionarTelaCadastrarLaudoAvaliacao() class=\"md-primary md-raised\">{{\'button-novo-laudo\' | translate}}</md-button><!-- Exibir este trecho apenas quando for ambiente de desenvolvimento--><md-button ng-style=\"ambienteDev ? {\'background-color\': \'rgb(249, 242, 214)\'} : {}\" ng-if=\"ambienteDev && usuarioLogado.AVALIACAO_INCLUIR_EDITAR_LAUDORELATORIO == \'true\'\" ng-click=elaborarLaudoRelatorioAvaliacao() class=\"md-primary md-raised m-l-15\"><label>{{\'label-elaborar-laudo\' | translate}}</label></md-button></div><div flex class=caixa-tabela-resultado><div layout=column><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=th-margin-pvgs style=\"margin-top: 10px; max-width: 160px\"><md-input-container class=md-block flex=100><label>{{\'tr-situacao\' | translate}}:</label><md-select ng-model=situacaoLaudoAvaliacaoList name=SituacaoLaudoAvaliacao md-on-close=consultarAvaliacoesLaudoAvaliacao() style=\"margin-bottom: 0 !important\" multiple><md-optgroup labe=ListaSituacaoLaudoAvaliacao><md-option ng-repeat=\"(k,v) in listaSituacoesLaudo\" ng-value=k>{{v}}</md-option></md-optgroup></md-select></md-input-container></th><th class=\"th-margin-pvgs lineBreak\" md-column>{{\'tr-data-assinatura\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-prazo-validade\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-revalidado\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-elaborado-por\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-responsavel-tecnico\' | translate}}</th><th class=\"th-margin-pvgs forceAlignRight\" md-column>{{\'tr-valor-do-imovel\' | translate}}</th><th md-column width=10 class=forceAlignRight>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"laudoAvaliacao in avaliacoesLaudoAvaliacao\"><td md-cell ng-show=\"laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_TECNICO\' && laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_CHEFIA\' && laudoAvaliacao.situacaoLaudo != \'EM_PROCESSO_HOMOLOGACAO\'\"><div class=em-linha>{{listaSituacoesLaudo[laudoAvaliacao.situacaoLaudo]}}</div></td><td class=th-margin-pvgs md-cell ng-show=\"laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_TECNICO\' || laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_CHEFIA\'\"><a ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'true\' || usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'true\' || usuarioLogado.AVALIACAO_ACOES_TECNICO == \'true\'\" href=\"\" ng-click=redirecionarConsultarAcoesAvaliacao(laudoAvaliacao) target=_blank>{{listaSituacoesLaudo[laudoAvaliacao.situacaoLaudo]}}</a><label ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'false\' && usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'false\' && usuarioLogado.AVALIACAO_ACOES_TECNICO == \'false\'\">{{listaSituacoesLaudo[laudoAvaliacao.situacaoLaudo]}}</label></td><td md-cell ng-bind=\"laudoAvaliacao.dataAssinatura | date: \'dd/MM/yyyy\' : \'-0300\'\"></td><td md-cell ng-bind=\"laudoAvaliacao.inicioPeriodo && laudoAvaliacao.finalPeriodo\n                                    ? (laudoAvaliacao.inicioPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') + \' a \' + (laudoAvaliacao.finalPeriodo | date: \'dd/MM/yyyy\' : \'-0300\')\n                                    : (laudoAvaliacao.inicioPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') + (laudoAvaliacao.finalPeriodo ? (laudoAvaliacao.finalPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') : \'\')\"></td><td md-cell ng-bind=\"(verificarLaudosRevalidados(laudoAvaliacao)) | simNao\"></td><td md-cell>{{listaElaboracaoLaudo[laudoAvaliacao.tipoLaudoElaborado]}}</td><td md-cell><div layout=column><label ng-repeat=\"responsavelTecnico in laudoAvaliacao.responsaveis\" ng-bind=\"responsavelTecnico.nome + \' - \' + responsavelTecnico.crea\"></label></div></td><td md-cell class=forceAlignRight>{{(laudoAvaliacao.valorImovel | currency).replace(\'R$\', \'\')}}<md-icon ng-if=\"laudoAvaliacao.laudoSpiunet == true\" class=\"icon alerta\" ng-bind=\"\'info_circle\'\" alt=\"{{\'label-laudo-migrado\' | translate}}\" aria-label=\"{{\'label-laudo-migrado\' | translate}}\"><md-tooltip>{{\'label-laudo-migrado\' | translate}}</md-tooltip></md-icon></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO === \'true\' && (usuarioLogado.QUALQUER_JURISDICAO === \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\"><md-button ng-click=redirecionarAssinarNotaTecnicaLaudoAvaliacao(laudoAvaliacao.id) class=\"md-fab md-raised md-mini\" ng-if=usuarioLogadoHomologador(laudoAvaliacao.homologadores) ng-show=\"laudoAvaliacao.situacaoLaudo==PENDENTE_HOMOLOGACAO\"><md-icon alt=\"{{\'label-assinar-nota-tecnica-homologacao\' | translate}}\" ng-bind=\"\'done\'\" aria-label=\"{{\'label-assinar-nota-tecnica-homologacao\' | translate}}\"><md-tooltip>{{\'label-assinar-nota-tecnica-homologacao\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarRevalidarLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.isRevalidar && (usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' || usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO_NAO_HABILITADO == \'true\')\"><md-icon ng-bind=\"\'refresh\'\" alt=\"{{\'label-revalidar-laudo\' | translate}}\" aria-label=\"{{\'label-revalidar-laudo\' | translate}}\"><md-tooltip>{{\'label-revalidar-laudo\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarRevalidarLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'REVALIDACAO_PREENCHIMENTO\' && (usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' || usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO_NAO_HABILITADO == \'true\')\"><md-icon ng-bind=\"\'create\'\" alt=\"{{\'label-editar-revalidar-laudo\' | translate}}\" aria-label=\"{{\'label-editar-revalidar-laudo\' | translate}}\"><md-tooltip>{{\'label-editar-revalidar-laudo\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=cancelarLaudoAvaliacao(laudoAvaliacao.id); ng-show=\"laudoAvaliacao.situacaoLaudo==\'VIGENTE\' || laudoAvaliacao.situacaoLaudo==\'VIGENTE_REVALIDADO\' || laudoAvaliacao.situacaoLaudo==\'VALIDO\' || laudoAvaliacao.situacaoLaudo==\'ATIVO_REVALIDADO\'\"><md-icon ng-bind=\"\'clear\'\" alt=\"{{\'label-cancelar-laudo\' | translate}}\" aria-label=\"{{\'label-cancelar-laudo\' | translate}}\"><md-tooltip>{{\'label-cancelar-laudo\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarTelaEditarLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'EM_PREENCHIMENTO\'\"><md-icon ng-bind=\"\'create\'\" alt=\"{{\'label-editar-laudo\' | translate}}\" aria-label=\"{{\'label-editar-laudo\' | translate}}\"><md-tooltip>{{\'label-editar-laudo\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=removerRascunhoLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'EM_PREENCHIMENTO\' || laudoAvaliacao.situacaoLaudo==\'REVALIDACAO_PREENCHIMENTO\'\"><md-icon ng-bind=\"\'delete\'\" alt=\"{{\'label-remover-rascunho\' | translate}}\" aria-label=\"{{\'label-remover-rascunho\' | translate}}\"><md-tooltip>{{\'label-remover-rascunho\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=excluirPendenciaLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'PENDENTE_VALIDACAO_CHEFIA\' || laudoAvaliacao.situacaoLaudo==\'PENDENTE_VALIDACAO_TECNICO\'\"><md-icon ng-bind=\"\'delete\'\" alt=\"{{\'label-excluir-pendencia\' | translate}}\" aria-label=\"{{\'label-excluir-pendencia\' | translate}}\"><md-tooltip>{{\'label-excluir-pendencia\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-show=\"laudoAvaliacao.situacaoLaudo!=\'EM_PREENCHIMENTO\' && laudoAvaliacao.situacaoLaudo!=\'PENDENTE_HOMOLOGACAO\' && laudoAvaliacao.showHistoricoAcoes\" class=\"md-fab md-raised md-mini\" ng-click=visualizarHistorico(laudoAvaliacao.id)><md-icon ng-bind=\"\'info\'\" alt=\"{{\'label-historico-acoes\' | translate}}\" aria-label=\"{{\'label-historico-acoes\' | translate}}\"><md-tooltip>{{\'label-historico-acoes\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarDetalharLaudo(laudoAvaliacao.id)><md-icon ng-bind=\"\'search\'\" alt=\"{{\'label-detalhar-laudo\' | translate}}\" aria-label=\"{{\'label-detalhar-laudo\' | translate}}\"><md-tooltip>{{\'label-detalhar-laudo\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions><md-fab-actions ng-if=\"(usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'false\' && usuarioLogado.AVALIACAO_CONSULTAR_VISUALIZAR_DADOS_AVALIACAO == \'true\') || (usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && usuarioLogado.QUALQUER_JURISDICAO == \'false\' && usuarioLogado.JURISDICOES.indexOf(uf) == -1)\"><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarDetalharLaudo(laudoAvaliacao.id)><md-icon ng-bind=\"\'search\'\" alt=\"{{\'label-detalhar-laudo\' | translate}}\" aria-label=\"{{\'label-detalhar-laudo\' | translate}}\"><md-tooltip>{{\'label-detalhar-laudo\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=consultarAvaliacoesLaudoAvaliacao md-page-select></md-table-pagination></md-conent></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarLaudoAvaliacaoLocacao.html","<div layout=row><md-button ng-click=redirecionarTelaCadastrarLaudoAvaliacao() class=\"md-primary md-raised\" ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\">{{\'button-novo-laudo\' | translate}}</md-button><md-button ng-if=\"usuarioLogado.AVALIACAO_INCLUIR_EDITAR_LAUDORELATORIO == \'true\'\" ng-click=elaborarLaudoRelatorioAvaliacao() class=\"md-primary md-raised m-l-15\"><label>{{\'label-elaborar-laudo\' | translate}}</label></md-button></div><div flex class=caixa-tabela-resultado><div layout=column><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=th-margin-pvgs style=\"margin-top: 10px; max-width: 160px\"><md-input-container class=md-block flex=100><label>{{\'tr-situacao\' | translate}}:</label><md-select ng-model=situacaoLaudoAvaliacaoList name=SituacaoLaudoAvaliacao ng-change=selecionarTodos() ng-blur=consultarAvaliacoesLaudoAvaliacao() style=\"margin-bottom: 0 !important\" multiple><md-optgroup labe=ListaSituacaoLaudoAvaliacao><md-option ng-repeat=\"(k,v) in listaSituacoesLaudo\" ng-value=k>{{v}}</md-option></md-optgroup></md-select></md-input-container></th><th class=\"th-margin-pvgs lineBreak\" md-column>{{\'tr-data-assinatura\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-periodo\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-elaborado-por\' | translate}}</th><th class=\"th-margin-pvgs forceAlignRight\" md-column>{{\'tr-valor-do-imovel\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-responsavel-tecnico\' | translate}}</th><th md-column width=10 class=forceAlignRight>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"laudoAvaliacao in avaliacoesLaudoAvaliacao\"><td md-cell ng-show=\"laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_TECNICO\' && laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_CHEFIA\' && laudoAvaliacao.situacaoLaudo != \'EM_PROCESSO_HOMOLOGACAO\'\">{{listaSituacoesLaudo[laudoAvaliacao.situacaoLaudo]}}</td><td class=th-margin-pvgs md-cell ng-show=\"laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_TECNICO\' || laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_CHEFIA\' || laudoAvaliacao.situacaoLaudo == \'EM_PROCESSO_HOMOLOGACAO\'\"><a ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'true\' || usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'true\' || usuarioLogado.AVALIACAO_ACOES_TECNICO == \'true\'\" href=\"\" ng-click=redirecionarConsultarAcoesAvaliacao(laudoAvaliacao) target=_blank>{{listaSituacoesLaudo[laudoAvaliacao.situacaoLaudo]}}</a><label ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'false\' && usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'false\' && usuarioLogado.AVALIACAO_ACOES_TECNICO == \'false\'\">{{listaSituacoesLaudo[laudoAvaliacao.situacaoLaudo]}}</label></td><td md-cell ng-bind=\"laudoAvaliacao.dataAssinatura | date: \'dd/MM/yyyy\' : \'-0300\'\"></td><td md-cell ng-bind=\"laudoAvaliacao.inicioPeriodo && laudoAvaliacao.finalPeriodo \n                                ? (laudoAvaliacao.inicioPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') + \' a \' + (laudoAvaliacao.finalPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') \n                                : (laudoAvaliacao.inicioPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') + (laudoAvaliacao.finalPeriodo ? (laudoAvaliacao.finalPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') : \'\')\"></td><td md-cell>{{listaElaboracaoLaudo[laudoAvaliacao.tipoLaudoElaborado]}}</td><td md-cell class=forceAlignRight>{{(laudoAvaliacao.valorImovel | currency).replace(\'R$\', \'\')}}</td><td md-cell><div layout=column><label ng-repeat=\"responsavelTecnico in laudoAvaliacao.responsaveis\" ng-bind=\"responsavelTecnico.nome + \' - \' + responsavelTecnico.crea\"></label></div></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\"><md-button ng-click=redirecionarAssinarNotaTecnicaLaudoAvaliacao(laudoAvaliacao.id) class=\"md-fab md-raised md-mini\" ng-if=usuarioLogadoHomologador(laudoAvaliacao.homologadores) ng-show=\"laudoAvaliacao.situacaoLaudo==PENDENTE_HOMOLOGACAO\"><md-icon alt=\"{{\'label-assinar-nota-tecnica-homologacao\' | translate}}\" ng-bind=\"\'done\'\" aria-label=\"{{\'label-assinar-nota-tecnica-homologacao\' | translate}}\"><md-tooltip>{{\'label-assinar-nota-tecnica-homologacao\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarAssinarRevalidacaoLaudoAvaliacao(laudoAvaliacao.id) ng-if=\"laudoAvaliacao.revalidacaoLaudoAvaliacao && usuarioLogadoHomologador(laudoAvaliacao.revalidacaoLaudoAvaliacao.responsaveis) && laudoAvaliacao.situacaoLaudo==EM_PROCESSO_REVALIDACAO\"><md-icon ng-bind=\"\'done\'\" alt=\"{{\'label-assinar-revalidacao\' | translate}}\" aria-label=\"{{\'label-assinar-revalidacao\' | translate}}\"><md-tooltip>{{\'label-assinar-revalidacao\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarRevalidarLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"(laudoRevalidacao.id == laudoAvaliacao.id) && (usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' || usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO_NAO_HABILITADO == \'true\')\"><md-icon ng-bind=\"\'refresh\'\" alt=\"{{\'label-revalidar-laudo\' | translate}}\" aria-label=\"{{\'label-revalidar-laudo\' | translate}}\"><md-tooltip>{{\'label-revalidar-laudo\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarRevalidarLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'REVALIDACAO_PREENCHIMENTO\' && (usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' || usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO_NAO_HABILITADO == \'true\')\"><md-icon ng-bind=\"\'create\'\" alt=\"{{\'label-editar-revalidar-laudo\' | translate}}\" aria-label=\"{{\'label-editar-revalidar-laudo\' | translate}}\"><md-tooltip>{{\'label-editar-revalidar-laudo\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=cancelarLaudoAvaliacao(laudoAvaliacao.id); ng-show=\"laudoAvaliacao.situacaoLaudo==\'VIGENTE\' || laudoAvaliacao.situacaoLaudo==\'VIGENTE_REVALIDADO\' || laudoAvaliacao.situacaoLaudo==\'VALIDO\' || laudoAvaliacao.situacaoLaudo==\'ATIVO_REVALIDADO\'\"><md-icon ng-bind=\"\'clear\'\" alt=\"{{\'label-cancelar-laudo\' | translate}}\" aria-label=\"{{\'label-cancelar-laudo\' | translate}}\"><md-tooltip>{{\'label-cancelar-laudo\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarDetalharLaudo(laudoAvaliacao.id)><md-icon ng-bind=\"\'search\'\" alt=\"{{\'label-detalhar-laudo\' | translate}}\" aria-label=\"{{\'label-detalhar-laudo\' | translate}}\"><md-tooltip>{{\'label-detalhar-laudo\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarTelaEditarLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'EM_PREENCHIMENTO\'\"><md-icon ng-bind=\"\'create\'\" alt=\"{{\'label-editar-laudo\' | translate}}\" aria-label=\"{{\'label-editar-laudo\' | translate}}\"><md-tooltip>{{\'label-editar-laudo\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=removerRascunhoLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'EM_PREENCHIMENTO\' || laudoAvaliacao.situacaoLaudo==\'REVALIDACAO_PREENCHIMENTO\'\"><md-icon ng-bind=\"\'delete\'\" alt=\"{{\'label-remover-rascunho\' | translate}}\" aria-label=\"{{\'label-remover-rascunho\' | translate}}\"><md-tooltip>{{\'label-remover-rascunho\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=excluirLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'PENDENTE_VALIDACAO_CHEFIA\' || laudoAvaliacao.situacaoLaudo==\'PENDENTE_VALIDACAO_TECNICO\' || laudoAvaliacao.situacaoLaudo==\'PENDENTE_HOMOLOGACAO\' || laudoAvaliacao.situacaoLaudo==\'EM_PROCESSO_HOMOLOGACAO\'\"><md-icon ng-bind=\"\'delete\'\" alt=\"{{\'label-excluir-laudo-locacao\' | translate}}\" aria-label=\"{{\'label-excluir-laudo-locacao\' | translate}}\"><md-tooltip>{{\'label-excluir-laudo-locacao\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-show=\"laudoAvaliacao.situacaoLaudo!=\'EM_PREENCHIMENTO\' && laudoAvaliacao.situacaoLaudo!=\'PENDENTE_HOMOLOGACAO\' && laudoAvaliacao.showHistoricoAcoes\" class=\"md-fab md-raised md-mini\" ng-click=visualizarHistorico(laudoAvaliacao.id)><md-icon ng-bind=\"\'info\'\" alt=\"{{\'label-historico-acoes\' | translate}}\" aria-label=\"{{\'label-historico-acoes\' | translate}}\"><md-tooltip>{{\'label-historico-acoes\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions><md-fab-actions ng-if=\"(usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'false\' && usuarioLogado.AVALIACAO_CONSULTAR_VISUALIZAR_DADOS_AVALIACAO == \'true\') || (usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && usuarioLogado.QUALQUER_JURISDICAO == \'false\' && usuarioLogado.JURISDICOES.indexOf(uf) == -1)\"><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarDetalharLaudo(laudoAvaliacao.id)><md-icon ng-bind=\"\'search\'\" alt=\"{{\'label-detalhar-laudo\' | translate}}\" aria-label=\"{{\'label-detalhar-laudo\' | translate}}\"><md-tooltip>{{\'label-detalhar-laudo\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=consultarAvaliacoesLaudoAvaliacao md-page-select></md-table-pagination></md-conent></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/adicionarRelatorioValorReferencia.html","<div layout=row><md-button ng-click=redirecionarTelaCadastrarRelatorioValorReferencia() class=\"md-primary md-raised\" ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\">{{\'button-novo-relatorio\' | translate}}</md-button><!-- Exibir este trecho apenas quando for ambiente de desenvolvimento--><md-button ng-style=\"ambienteDev ? {\'background-color\': \'rgb(249, 242, 214)\'} : {}\" ng-if=\"ambienteDev && usuarioLogado.AVALIACAO_INCLUIR_EDITAR_LAUDORELATORIO == \'true\'\" ng-click=elaborarLaudoRelatorioAvaliacao() class=\"md-primary md-raised m-l-15\"><label>{{\'label-elaborar-relatorio\' | translate}}</label></md-button></div><div flex class=caixa-tabela-resultado><div layout=column><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=th-margin-pvgs style=\"margin-top: 10px; max-width: 160px\"><md-input-container class=md-block flex=100><label>{{\'tr-situacao\' | translate}}:</label><md-select ng-model=situacaoLaudoAvaliacaoList name=SituacaoLaudoAvaliacao md-on-close=consultarAvaliacoesLaudoAvaliacao() style=\"margin-bottom: 0 !important\" multiple><md-optgroup labe=ListaSituacaoLaudoAvaliacao><md-option ng-repeat=\"(k,v) in listaSituacoesLaudo\" ng-value=k>{{v}}</md-option></md-optgroup></md-select></md-input-container></th><th class=\"th-margin-pvgs lineBreak\" md-column>{{\'tr-data-assinatura\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-prazo-validade\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-revalidado\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-elaborado-por\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-responsavel-tecnico\' | translate}}</th><th class=\"th-margin-pvgs forceAlignRight\" md-column>{{\'tr-valor-do-imovel\' | translate}}</th><th md-column width=10 class=forceAlignRight>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"laudoAvaliacao in avaliacoesLaudoAvaliacao\"><td md-cell ng-show=\"laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_TECNICO\' && laudoAvaliacao.situacaoLaudo != \'PENDENTE_VALIDACAO_CHEFIA\' && laudoAvaliacao.situacaoLaudo != \'EM_PROCESSO_HOMOLOGACAO\'\"><div class=em-linha>{{listaSituacoesLaudo[laudoAvaliacao.situacaoLaudo]}}</div></td><td class=th-margin-pvgs md-cell ng-show=\"laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_TECNICO\' || laudoAvaliacao.situacaoLaudo == \'PENDENTE_VALIDACAO_CHEFIA\' || laudoAvaliacao.situacaoLaudo == \'EM_PROCESSO_HOMOLOGACAO\'\"><a ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'true\' || usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'true\' || usuarioLogado.AVALIACAO_ACOES_TECNICO == \'true\'\" href=\"\" ng-click=redirecionarConsultarAcoesAvaliacao(laudoAvaliacao) target=_blank>{{listaSituacoesLaudo[laudoAvaliacao.situacaoLaudo]}}</a><label ng-if=\"usuarioLogado.AVALIACAO_ACOES_SUPERINTENDENTE == \'false\' && usuarioLogado.AVALIACAO_ACOES_CHEFIA == \'false\' && usuarioLogado.AVALIACAO_ACOES_TECNICO == \'false\'\">{{listaSituacoesLaudo[laudoAvaliacao.situacaoLaudo]}}</label></td><td md-cell ng-bind=\"laudoAvaliacao.dataAssinatura | date: \'dd/MM/yyyy\' : \'-0300\'\"></td><td md-cell ng-bind=\"laudoAvaliacao.inicioPeriodo && laudoAvaliacao.finalPeriodo\n                                    ? (laudoAvaliacao.inicioPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') + \' a \' + (laudoAvaliacao.finalPeriodo | date: \'dd/MM/yyyy\' : \'-0300\')\n                                    : (laudoAvaliacao.inicioPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') + (laudoAvaliacao.finalPeriodo ? (laudoAvaliacao.finalPeriodo | date: \'dd/MM/yyyy\' : \'-0300\') : \'\')\"></td><td md-cell ng-bind=\"(verificarLaudosRevalidados(laudoAvaliacao)) | simNao\"></td><td md-cell>{{listaElaboracaoLaudo[laudoAvaliacao.tipoLaudoElaborado]}}</td><td md-cell><div layout=column><label ng-repeat=\"responsavelTecnico in laudoAvaliacao.responsaveis\" ng-bind=\"responsavelTecnico.nome + \' - \' + responsavelTecnico.crea\"></label></div></td><td md-cell class=forceAlignRight>{{(laudoAvaliacao.valorImovel | currency).replace(\'R$\', \'\')}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions ng-if=\"usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && (usuarioLogado.QUALQUER_JURISDICAO == \'true\' || usuarioLogado.JURISDICOES.indexOf(uf) != -1)\"><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarRevalidarLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.isRevalidar && (usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' || usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO_NAO_HABILITADO == \'true\')\"><md-icon ng-bind=\"\'refresh\'\" alt=\"{{\'label-revalidar-relatorio\' | translate}}\" aria-label=\"{{\'label-revalidar-relatorio\' | translate}}\"><md-tooltip>{{\'label-revalidar-relatorio\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarRevalidarLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'REVALIDACAO_PREENCHIMENTO\' && (usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' || usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO_NAO_HABILITADO == \'true\')\"><md-icon ng-bind=\"\'create\'\" alt=\"{{\'label-editar-revalidar-laudo\' | translate}}\" aria-label=\"{{\'label-editar-revalidar-laudo\' | translate}}\"><md-tooltip>{{\'label-editar-revalidar-laudo\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=cancelarLaudoAvaliacao(laudoAvaliacao.id); ng-show=exibirCancelar(laudoAvaliacao.situacaoLaudo)><md-icon ng-bind=\"\'clear\'\" alt=\"{{\'label-cancelar-laudo\' | translate}}\" aria-label=\"{{\'label-cancelar-laudo\' | translate}}\"><md-tooltip>{{\'label-cancelar-relatorio\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarTelaEditarLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'EM_PREENCHIMENTO\'\"><md-icon ng-bind=\"\'create\'\" alt=\"{{\'label-editar-relatorio\' | translate}}\" aria-label=\"{{\'label-editar-relatorio\' | translate}}\"><md-tooltip>{{\'label-editar-relatorio\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=removerRascunhoLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'EM_PREENCHIMENTO\' || laudoAvaliacao.situacaoLaudo==\'REVALIDACAO_PREENCHIMENTO\'\"><md-icon ng-bind=\"\'delete\'\" alt=\"{{\'label-remover-rascunho\' | translate}}\" aria-label=\"{{\'label-remover-rascunho\' | translate}}\"><md-tooltip>{{\'label-remover-rascunho\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=excluirPendenciaLaudoAvaliacao(laudoAvaliacao.id) ng-show=\"laudoAvaliacao.situacaoLaudo==\'PENDENTE_VALIDACAO_CHEFIA\' || laudoAvaliacao.situacaoLaudo==\'PENDENTE_VALIDACAO_TECNICO\'\"><md-icon ng-bind=\"\'delete\'\" alt=\"{{\'label-excluir-pendencia\' | translate}}\" aria-label=\"{{\'label-excluir-pendencia\' | translate}}\"><md-tooltip>{{\'label-excluir-pendencia\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-show=\"laudoAvaliacao.situacaoLaudo!=\'EM_PREENCHIMENTO\' && laudoAvaliacao.situacaoLaudo!=\'PENDENTE_HOMOLOGACAO\' && laudoAvaliacao.showHistoricoAcoes\" class=\"md-fab md-raised md-mini\" ng-click=visualizarHistorico(laudoAvaliacao.id)><md-icon ng-bind=\"\'info\'\" alt=\"{{\'label-historico-acoes\' | translate}}\" aria-label=\"{{\'label-historico-acoes\' | translate}}\"><md-tooltip>{{\'label-historico-acoes\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarDetalharLaudo(laudoAvaliacao.id)><md-icon ng-bind=\"\'search\'\" alt=\"{{\'label-detalhar-relatorio\' | translate}}\" aria-label=\"{{\'label-detalhar-relatorio\' | translate}}\"><md-tooltip>{{\'label-detalhar-relatorio\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions><md-fab-actions ng-if=\"(usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'false\' && usuarioLogado.AVALIACAO_CONSULTAR_VISUALIZAR_DADOS_AVALIACAO == \'true\') || (usuarioLogado.AVALIACAO_MANTER_DADOS_AVALIACAO == \'true\' && usuarioLogado.QUALQUER_JURISDICAO == \'false\' && usuarioLogado.JURISDICOES.indexOf(uf) == -1)\"><md-button class=\"md-fab md-raised md-mini\" ng-click=redirecionarDetalharLaudo(laudoAvaliacao.id)><md-icon ng-bind=\"\'search\'\" alt=\"{{\'label-detalhar-relatorio\' | translate}}\" aria-label=\"{{\'label-detalhar-relatorio\' | translate}}\"><md-tooltip>{{\'label-detalhar-relatorio\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=consultarAvaliacoesLaudoAvaliacao md-page-select></md-table-pagination></md-conent></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/apresentarValorDominioPleno.html","<div flex class=caixa-tabela-resultado><div layout=column ng-show=\"listaValoresDominioPleno.length > 0\"><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th class=th-margin-pvgs md-column>{{\'tr-periodo\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-fonte\' | translate}}</th><th class=th-margin-pvgs md-column>{{\'tr-valor\' | translate}} m² (R$)</th><th class=th-margin-pvgs md-column>{{\'tr-area-terreno-fracao\' | translate}} (m²)</th><th class=th-margin-pvgs md-column>{{\'tr-valor-do-terreno\' | translate}} (R$)</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listaValoresDominioPleno\"><td class=th-margin-pvgs md-cell><span ng-show=\"($index == 0 && registroInicial == 1)\">{{(item.dataInicial | date: \'dd/MM/yyyy\' : \'-0300\')}}</span> <span ng-show=\"!($index == 0 && registroInicial == 1)\">{{(item.dataInicial | date: \'dd/MM/yyyy\' : \'-0300\') + (item.dataFinal ? \' a \' : \'\') + (item.dataFinal | date: \'dd/MM/yyyy\' : \'-0300\')}}</span></td><td class=th-margin-pvgs md-cell>{{item.tipo != \'SPU\' ? item.tipo : \'PVG/SPU\'}}</td><td class=th-margin-pvgs-centralizada md-cell>{{(item.valor | currency).replace(\'R$\', \'\')}}</td><td class=th-margin-pvgs-centralizada md-cell>{{(item.areaTerreno | currency).replace(\'R$\', \'\')}}</td><td class=th-margin-pvgs-centralizada md-cell>{{(item.valorTerreno | currency).replace(\'R$\', \'\')}}</td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=buscarValorDominioPleno md-page-select></md-table-pagination></md-conent></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/dadosComplementaresAmostra.tmpl.html","<div class=upload-arquivos ng-show=\"!detalharAmostra || paginationDadosComplementares.length > 0\"><div layout=row layout-xs=column><h3 class=subtitulo-pagina>{{\'label-dados-complementares\' | translate}}</h3></div><div class=upload-body layout=row layout-xs=column ng-show=!detalharAmostra><md-input-container class=md-block flex=\"{{dadoComplementar.tipoDadoComplementar.nome == \'Outro\' ? \'50\' : \'100\'}}\"><label>{{\'label-tipo\' | translate}}*:</label><md-select ng-model=dadoComplementar.tipoDadoComplementar name=\"{{nomeCampo}} - Tipo Dados Complementares\" ng-disabled=\"listaTiposDadosComplementares.length == undefined || detalharAmostra\"><md-option ng-repeat=\"item in listaTiposDadosComplementares\" ng-value=item>{{item.nome}}</md-option></md-select></md-input-container><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block layout-sm=column ng-if=\"dadoComplementar.tipoDadoComplementar.nome == \'Outro\'\"><label>{{\'label-descricao-outro-dado-compelmentar\' | translate}}*:</label><input type=text class=barraRolagemTextArea maxlength=50 ng-disabled=detalharAmostra ng-model=dadoComplementar.descricaoOutros name=\"{{nomeCampo}} - {{\'label-descricao\' | translate}}\"></md-input-container></div><div class=upload-body layout=row layout-xs=column ng-show=\"!detalharAmostra && !gerarPlanilha\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100 flex-offset=5><label>{{\'label-dado-complementar\' | translate}}</label><textarea class=barraRolagemTextArea ng-model=dadoComplementar.descricao ng-disabled=detalharAmostra ui-mask-placeholder-char=space></textarea></md-input-container></div><div layout=row layout-xs=column ng-show=!detalharAmostra><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button ng-show=\"!detalharAmostra && !editar.buttonShow\" class=\"md-primary md-raised add-button\" ng-click=adicionarDadoComplementar() style=\"font-weight:normal; text-transform:none\"><label>{{\'button-adicionar\' | translate}}</label></md-button><md-button ng-show=\"!detalharAmostra && editar.buttonShow\" class=\"md-primary md-raised add-button\" ng-click=gravarAlteracaoDadoComplementar() style=\"font-weight:normal; text-transform:none\"><label>{{\'button-alterar\' | translate}}</label></md-button></div></div></div><div flex class=caixa-tabela-fieldset ng-show=\"paginationDadosComplementares.length > 0\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th ng-style=\"gerarPlanilha ? \'width: 90%\' : \'width: 45%\'\" class=th-margin-pvgs md-column>{{\'label-tipo\' | translate}}</th><th style=\"width: 45%\" class=th-margin-pvgs ng-if=!gerarPlanilha md-column>{{\'label-dado-complementar\' | translate}}</th><th style=\"width: 10%\" ng-show=!detalharAmostra class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in paginationDadosComplementares\"><td md-cell ng-bind=\"item.tipoDadoComplementar.nome != \'Outro\' ? item.tipoDadoComplementar.nome : item.descricaoOutros\"></td><td md-cell ng-if=!gerarPlanilha ng-bind=item.descricao></td><td md-cell ng-show=!detalharAmostra><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=alterarDadoComplementar($index) ng-show=!gerarPlanilha class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-editar-norma\' | translate}}\" ng-bind=\"\'edit\'\" aria-label=\"{{\'label-editar-norma\' | translate}}\"><md-tooltip>{{\'label-editar-norma\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=removerDadoComplementar($index) class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir-norma\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir-norma\' | translate}}\"><md-tooltip>{{\'label-excluir-norma\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoDadosComplementares md-page-select></md-table-pagination></md-content></div></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/documentoEdificacaoBenfeitoria.tmpl.html","<ng-form name=formularioDocumentos><fieldset class=fieldsetArredondado><div layout=row flex layout-align=\"start start\" class=m-t-20><h3 class=subtitulo-pagina>{{titulo}}</h3></div><div flex layout=row layout-align=\"start start\" ng-show=!detalharLaudoRelatorio><md-button ng-show=isBotaoIncluirNovoDocumento class=\"md-primary md-raised\" ng-click=inserirNovoDocumento(); style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-inserir-novo-documento\' | translate}}</label></md-button></div><div flex class=caixa-tabela-resultado ng-show=\"paginationDocumentos.length > 0\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 40%\" class=th-margin-pvgs md-column>{{\'label-documento\' | translate}}</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'label-possui\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-data-emissao\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-data-validade\' | translate}}</th><th style=\"width: 5%\" ng-show=!detalharLaudoRelatorio class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documentoEdificacao in paginationDocumentos\"><td md-cell class=th-margin-pvgs ng-bind=documentoEdificacao.nome></td><td md-cell class=th-margin-pvgs><label ng-show=\"documentoEdificacao.possui == \'NAO\'\">Sim</label><label ng-show=\"documentoEdificacao.possui == \'SIM\'\">Não</label><label ng-show=\"documentoEdificacao.possui == \'SEM_INFORMACAO\'\">Sem informação</label></td><td md-cell><label ng-bind=documentoEdificacao.dataEmissao></label></td><td md-cell><label ng-bind=documentoEdificacao.dataValidade></label></td><td md-cell ng-show=!detalharLaudoRelatorio><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=alterarDocumentoEdificacao($index) ng-show=!detalharLaudoRelatorio class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-editar\' | translate}}\" ng-bind=\"\'edit\'\" aria-label=\"{{\'label-editar\' | translate}}\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=\"removerDocumento($index, documentoEdificacao.arquivo.id)\" ng-show=\"!detalharLaudoRelatorio && documentoEdificacao.isPossui != \'SEM_INFORMACAO\'\" class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir-documento\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoDocumentos md-page-select></md-table-pagination></md-content></div></div></fieldset></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/documentoEdificacaoUnidadeAutonoma.tmpl.html","<ng-form name=formularioDocumentos><fieldset class=fieldsetArredondado><div layout=row flex layout-align=\"start start\" class=m-t-20><h3 class=subtitulo-pagina>{{titulo}}</h3></div><div flex layout=row layout-align=\"start start\" ng-show=!detalharLaudoRelatorio><md-button ng-show=isBotaoIncluirNovoDocumento class=\"md-primary md-raised\" ng-click=inserirNovoDocumento(); style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-inserir-novo-documento\' | translate}}</label></md-button></div><div flex class=caixa-tabela-resultado ng-show=\"paginationDocumentos.length > 0\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 40%\" class=th-margin-pvgs md-column>{{\'label-documento\' | translate}}</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'label-possui\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-data-emissao\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-data-validade\' | translate}}</th><th style=\"width: 5%\" ng-show=!detalharLaudoRelatorio class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documentoEdificacao in paginationDocumentos\"><td md-cell class=th-margin-pvgs ng-bind=documentoEdificacao.nome></td><td md-cell class=th-margin-pvgs><label ng-show=\"documentoEdificacao.possui == \'NAO\'\">Sim</label><label ng-show=\"documentoEdificacao.possui == \'SIM\'\">Não</label><label ng-show=\"documentoEdificacao.possui == \'SEM_INFORMACAO\'\">Sem informação</label></td><td md-cell><label ng-bind=documentoEdificacao.dataEmissao></label></td><td md-cell><label ng-bind=documentoEdificacao.dataValidade></label></td><td md-cell ng-show=!detalharLaudoRelatorio><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=alterarDocumentoEdificacao($index) ng-show=!detalharLaudoRelatorio class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-editar\' | translate}}\" ng-bind=\"\'edit\'\" aria-label=\"{{\'label-editar\' | translate}}\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=\"removerDocumento($index, documentoEdificacao.arquivo.id)\" ng-show=!detalharLaudoRelatorio class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir-documento\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoDocumentos md-page-select></md-table-pagination></md-content></div></div></fieldset></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/documentoFonteAmostra.tmpl.html","<ng-form name=formularioDocumentos><div class=upload-arquivos ng-show=\"!detalharAmostra || paginationDocumentos.length > 0\"><div layout=row layout-xs=column ng-if=titulo><h3 class=subtitulo-pagina>{{titulo}}</h3></div><div class=upload-body layout=row layout-xs=column ng-show=!detalharAmostra><md-input-container class=md-block flex=100 ng-class=\"formEnviado && !documento.tipoFonte ? \'md-input-invalid\' : \'\'\"><label>{{\'label-tipo\' | translate}}:</label><md-select ng-model=documento.tipoFonte ng-change=changeTipoFonte(documento.tipoFonte) name=\"{{nomeCampo}} - Tipo documento\" ng-disabled=detalharAmostra><md-option ng-repeat=\"(k,v) in listaTiposDocumentos\" ng-value=k>{{v}}</md-option></md-select></md-input-container></div><div class=upload-body layout=row layout-xs=column ng-if=\"documento.tipoFonte == \'LINK\'\"><md-input-container layout-xs=row flex=66 flex-xs flex-sm class=md-block layout-xs=column layout-sm=column ng-class=\"formEnviado && !documento.link ? \'md-input-invalid\' : \'\'\"><label>{{\'label-link-fonte\' | translate}}</label><input ng-disabled=detalharAmostra ng-model=documento.link name=\"{{nomeCampo}} - {{\'label-link-fonte\' | translate}}\" ng-required=\"documento.tipoFonte == \'LINK\'\"></md-input-container><md-container class=md-block flex=33 layout=column flex-xs flex-sm layout-xs=row layout-sm=row ng-class=\"formEnviado && !documento.dtArquivo ? \'md-input-invalid\' : \'\'\"><md-datepicker md-max-date=dataAtual ng-disabled=detalharAmostra ng-model=documento.dtArquivo md-placeholder=\"{{\'placeholder-data\' | translate}}*\" class=\"datePickerClass m-b-25\"></md-datepicker></md-container></div><div class=upload-body layout=row layout-xs=column ng-if=\"documento.tipoFonte == \'ANEXO\'\"><md-input-container class=md-block flex=33 flex-xs=100 ng-class=\"formEnviado && !documento.nome ? \'md-input-invalid\' : \'\'\"><label>{{customNomeLabel ? customNomeLabel : \'label-nome\' | translate}}*</label><input ng-model=documento.nome type=text ng-disabled=detalharAmostra></md-input-container><md-container class=md-block flex=33 layout=column flex-xs flex-sm layout-xs=row layout-sm=row ng-class=\"formEnviado && !documento.dtArquivo ? \'md-input-invalid\' : \'\'\"><md-datepicker md-max-date=dataAtual ng-disabled=detalharAmostra ng-model=documento.dtArquivo md-placeholder=\"{{\'placeholder-data\' | translate}}*\" class=\"datePickerClass m-b-25\"></md-datepicker></md-container><div class=md-block flex-gt-xs flex=33><md-button class=\"md-primary md-raised upload-button\" type=button ng-disabled=\"detalharAmostra || documento.arquivo.nome\" ngf-multiple=false ngf-select=adicionarArquivoDocumento($files)><label>{{customButtonLabel ? customButtonLabel : \'button-selecionar-arquivo\' | translate}}</label></md-button></div></div><div layout=row layout-xs=column ng-show=!detalharAmostra><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised add-button\" type=button ng-click=adicionarListaDocumentos(formularioDocumentos) ng-disabled=detalharAmostra><label>{{\'button-adicionar-arquivo\' | translate}}</label></md-button></div></div></div><div flex class=caixa-tabela-fieldset ng-show=\"paginationDocumentos.length > 0\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 30%\" class=th-margin-pvgs md-column>{{\'label-tipo\' | translate}}</th><th style=\"width: 30%\" class=th-margin-pvgs md-column>{{\'label-data\' | translate}}</th><th style=\"width: 35%\" class=th-margin-pvgs md-column>{{\'label-fonte\' | translate}}</th><th style=\"width: 5%\" ng-show=!detalharAmostra class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in paginationDocumentos\"><td md-cell class=th-margin-pvgs ng-bind=listaTiposDocumentos[documento.tipoFonte]></td><td md-cell><label ng-bind=\"documento.dtArquivo | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><a ng-show=\"documento.tipoFonte == \'ANEXO\'\" ng-href={{baixarArquivo(documento.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=documento.arquivo.nome></label></a><a ng-show=\"documento.tipoFonte == \'LINK\'\" ng-href={{documento.link}} target=_blank><label class=mouse-pointer ng-bind=documento.link></label></a></td><td md-cell ng-show=!detalharAmostra><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"removerDocumento($index, documento.arquivo.id);\" ng-show=!detalharAmostra class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir-documento\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoDocumentos md-page-select></md-table-pagination></md-content></div></div></div></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/documentoLaudoAvaliacaoPadrao.tmpl.html","<ng-form name=formularioDocumentos><div class=upload-arquivos ng-show=\"!detalharLaudoAvaliacao || paginationDocumentos.length > 0\"><div layout=row layout-xs=column ng-if=titulo><h3 class=subtitulo-pagina>{{titulo}}</h3><md-tooltip md-direction=top ng-if=hint>{{hint}}</md-tooltip></div><div class=upload-body layout=row layout-xs=column ng-show=!detalharLaudoAvaliacao ng-if=fileLimitValido()><md-input-container class=md-block flex=\"{{campoData ? \'33\' : \'50\'}}\" flex-xs=100 ng-class=\"formEnviado && !documento.nome ? \'md-input-invalid\' : \'\'\"><label>{{customNomeLabel ? customNomeLabel : \'label-nome\' | translate}}*:</label><input ng-model=documento.nome type=text maxlength=50 ng-disabled=detalharLaudoAvaliacao></md-input-container><md-container class=md-block flex=33 layout=column flex-xs flex-sm layout-xs=row layout-sm=row ng-show=campoData ng-class=\"formEnviado && !documento.dtArquivo ? \'md-input-invalid\' : \'\'\"><md-datepicker md-max-date=dataAtual ng-disabled=detalharLaudoAvaliacao ng-model=documento.dtArquivo md-placeholder=\"{{\'placeholder-data\' | translate}}*\" class=\"datePickerClass m-b-25\"></md-datepicker></md-container><div class=md-block flex-gt-xs flex=\"{{campoData ? \'33\' : \'50\'}}\" ng-if=!documento.arquivo.nome><md-button class=\"md-primary md-raised upload-button\" type=button ng-disabled=\"detalharLaudoAvaliacao || documento.arquivo.nome\" ngf-multiple=false ngf-select=adicionarArquivoDocumento($files)><label>{{customButtonLabel ? customButtonLabel : \'button-selecionar-arquivo\' | translate}}</label></md-button></div><div class=md-block flex-gt-xs flex=\"{{campoData ? \'33\' : \'50\'}}\" ng-if=documento.arquivo.nome><label class=add-button>{{documento.arquivo.nome}}</label><md-button ng-click=\"documento.arquivo=[]\"><md-icon class=\"md-primary md-raised upload-button\" ng-bind=\"\'delete\'\" ng-click=\"documento.arquivo=[]\" alt=\"{{\'label-excluir-arquivo\' | translate}}\" aria-label=\"{{\'label-excluir-arquivo\' | translate}}\"><md-tooltip>{{\'label-excluir-arquivo\' | translate}}</md-tooltip></md-icon></md-button></div></div><div layout=row layout-xs=column ng-show=!detalharLaudoAvaliacao><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised add-button\" type=button ng-click=adicionarListaDocumentos(formularioDocumentos) ng-disabled=detalharLaudoAvaliacao><label>{{\'button-adicionar-arquivo\' | translate}}</label></md-button></div></div></div><div layout=row layout-xs=column class=upload-table ng-show=\"paginationDocumentos.length > 0\"><div flex><md-table-container><table md-table><tbody md-body><tr md-row ng-repeat=\"documentoLaudo in paginationDocumentos\"><td md-cell><label ng-bind=documentoLaudo.nome></label></td><td md-cell ng-show=campoData><label ng-bind=\"documentoLaudo.dtArquivo | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><a ng-href={{baixarArquivo(documentoLaudo.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=documentoLaudo.arquivo.nome></label></a></td><td md-cell ng-show=!detalharLaudoAvaliacao width=50px><a ng-click=\"removerDocumento($index, documentoLaudo.arquivo.id);\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></div></div></div></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/documentoSei.tmpl.html","<ng-form name=formularioDocumentos><div class=upload-arquivos ng-show=\"!detalharLaudoAvaliacao || paginationDocumentos.length > 0\"><div layout=row layout-xs=column ng-if=titulo><h3 class=subtitulo-pagina>{{titulo}}</h3></div><div class=upload-body layout=row layout-xs=column ng-show=!detalharLaudoAvaliacao ng-if=fileLimitValido()><md-input-container class=md-block flex=50 flex-xs=100 ng-class=\"formEnviado && !documento.numeroDocumentoSei ? \'md-input-invalid\' : \'\'\"><label>{{customNomeLabel ? customNomeLabel : \'label-numero-documento-sei\' | translate}}*:</label><input ng-model=documento.numeroDocumentoSei type=text ui-mask=0 maxlength=50 ng-disabled=detalharLaudoAvaliacao></md-input-container><div class=md-block flex-gt-xs flex=50><md-button class=\"md-primary md-raised upload-button\" type=button ng-click=adicionarListaDocumentos(formularioDocumentos) ng-disabled=detalharLaudoAvaliacao><label>{{customButtonLabel ? customButtonLabel : \'button-adicionar-arquivo\' | translate}}</label></md-button></div></div><div layout=row layout-xs=column ng-show=\"paginationDocumentos.length > 0\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 45%\" md-column>{{\'label-nome\' | translate}}</th><th style=\"width: 45%\" md-column>{{\'label-n-documento-sei\' | translate}}</th><th style=\"width: 10%\" ng-show=!detalharLaudoAvaliacao md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documentoLaudo in paginationDocumentos\"><td md-cell><label ng-bind=documentoLaudo.nome></label></td><td md-cell><a ng-href={{documentoLaudo.linkAcessoDocumentoSei}} target=_blank><label class=mouse-pointer ng-bind=documentoLaudo.numeroDocumentoSei></label></a></td><td md-cell ng-show=!detalharLaudoAvaliacao width=50px><a ng-click=removerDocumento($index);><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></div></div></div></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/fotosAvaliacaoImovel.html","<div layout=column class=carousel><div flex=100 layout=row ng-show=\"listaImagensImovel.length > 0\"><div flex=15 class=\"item volta\"><md-button ng-disabled=\"selectedIndex==0\" ng-click=imagemAnterior() class=md-icon-button aria-label=Anterior layout-align=\"start center\"><ng-md-icon icon=navigate_before size=30></ng-md-icon></md-button></div><div align=center flex=70 class=imagem><img ng-src={{gerarImagem(listaImagensImovel[selectedIndex].imagem);}}></div><div flex=15 class=\"item avanca\"><md-button ng-disabled=\"selectedIndex==listaImagensImovel.length-1\" ng-click=proximaImagem() class=md-icon-button aria-label=Próximo layout-align=\"end center\"><ng-md-icon icon=navigate_next size=30></ng-md-icon></md-button></div></div><div layout=row flex class=bolas layout-align=\"center center\"><div ng-repeat=\"i in listaImagensImovel track by $index\"><ng-md-icon icon=\"{{$index==selectedIndex ? \'lens\':\'panorama_fisheye\'}}\" size=10></ng-md-icon></div></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-formulario/templates/inserirValoresTerrenoBenfeitoria.html","<form name=InserirValoresTerrenoBenfeitoria><div ng-show=\"(listaBenfeitoriasImovel && listaBenfeitoriasImovel.length > 0) || (unidadeAutonomaImovel && unidadeAutonomaImovel.length === 1)\" layout=column flex><div layout=row layout-xs=column class=m-t-20><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>{{\'valores-terreno-benfeitoria\' | translate}}:</span></span></div></div><md-radio-group flex class=\"tamanho-radio-button-avaliacao md-block\" ng-model=tipoValorLaudo ng-change=limparDadosUnidadeAutonomaEBenfeitorias();buscarValorDominioPleno(); name=\"{{nomeCampo}} - {{\'valores-terreno-benfeitoria\' | translate}}\"><div layout=row><md-radio-button class=\"tamanho-radio-button-avaliacao md-primary\" flex=20 ng-disabled=\"detalharLaudoRelatorio && paisImovel!=BRASIL\" ng-value=INFORMAR_VALORES_LAUDO><div class=texto-preto>{{\'radio-valores-laudo\' | translate}}</div></md-radio-button><md-radio-button ng-show=mostrarEstimarValorTerreno class=\"tamanho-radio-button-avaliacao md-primary\" flex=20 ng-disabled=\"detalharLaudoRelatorio && paisImovel.pais!=BRASIL\" ng-value=ESTIMAR_VALORES_TERRENO><div class=texto-preto>{{\'radio-estimar-valores-terreno\' | translate}}</div></md-radio-button></div></md-radio-group></div><div layout=column flex=30 flex-xs=100 ng-if=\"tipoValorLaudo==INFORMAR_VALORES_LAUDO\"><md-input-container class=md-block><label class=sidenavTitulos>{{\'label-valor-terreno\' | translate}} {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}}:</label><input type=value ng-model=$parent.valorTerreno name=\"{{nomeCampo}} - {{\'label-valor-terreno\' | translate}}\" ui-number-mask=2 ng-blur=validarValorImovel() ng-required=\"$parent.listaBenfeitoriasImovel.length >= 1 && tipoValorLaudo==INFORMAR_VALORES_LAUDO\" ng-disabled=detalharLaudoRelatorio></md-input-container></div><div class=\"m-t-20 m-b-20\" ng-if=\"tipoValorLaudo == ESTIMAR_VALORES_TERRENO\" layout=column flex=50><label class=\"sidenavTitulos marginTopHomologadorNome\" layout=row>{{\'label-valor-estimado-terreno\' | translate}}:</label><div layout=row>{{moeda ? moeda.simbolo : listaMoedas[0].simbolo}} {{(valorTerreno | currency).replace(\'R$\',\'\')}}<div class=prc-valor-estimado><span ng-bind=\"\'(\' +(((valorTerreno*100)/valorAdotadoSelecionado) | number:2) + \'% do valor do imóvel)\'\"></span></div></div></div><table-informar-valor-laudo-benfeitorias ng-if=\"tipoValorLaudo == INFORMAR_VALORES_LAUDO && listaBenfeitoriasImovel && listaBenfeitoriasImovel.length > 0 && tipoLaudo==LAUDO_AVALIACAO\" nome-campo=$parent.nomeCampo benfeitorias-imovel=listaBenfeitoriasImovel detalhar-laudo-relatorio=$parent.detalharLaudoRelatorio redirecionar-informar-valor-cub=redirecionarInformarValorCub valor-adotado-selecionado=valorAdotadoSelecionado tipo-valor-laudo=$parent.tipoValorLaudo valor-terreno=$parent.valorTerreno total-valor-terreno=$parent.totalValorTerreno moeda=$parent.moeda lista-moedas=$parent.listaMoedas termo-responsabilidade=$parent.termoResponsabilidade></table-informar-valor-laudo-benfeitorias><!-- <table-estimar-valor-laudo-benfeitorias ng-if=\"tipoValorLaudo == ESTIMAR_VALORES && listaBenfeitoriasImovel && listaBenfeitoriasImovel.length > 0 && tipoLaudo==LAUDO_AVALIACAO\" nome-campo=\"$parent.nomeCampo\"\n                                            benfeitorias-imovel=\"$parent.listaBenfeitoriasImovel\" detalhar-laudo-relatorio=\"$parent.detalharLaudoRelatorio\"\n                                            redirecionar-informar-valor-cub=\"$parent.redirecionarInformarValorCub\" tipo-valor-laudo=\"$parent.tipoValorLaudo\" moeda=\"$parent.moeda\" lista-moedas=\"$parent.listaMoedas\"></table-estimar-valor-laudo-benfeitorias>\n --><table-informar-valor-laudo-relatorio-benfeitorias ng-if=\"tipoValorLaudo == INFORMAR_VALORES_LAUDO && listaBenfeitoriasImovel && listaBenfeitoriasImovel.length > 0 && tipoLaudo==LAUDO_RELATORIO\" nome-campo=$parent.nomeCampo redirecionar-informar-valor-cub=$parent.redirecionarInformarValorCub benfeitorias-imovel=$parent.listaBenfeitoriasImovel detalhar-laudo-relatorio=$parent.detalharLaudoRelatorio valor-adotado-selecionado=$parent.valorAdotadoSelecionado tipo-valor-laudo=$parent.tipoValorLaudo valor-terreno=$parent.valorTerreno total-valor-terreno=$parent.totalValorTerreno moeda=$parent.moeda lista-moedas=$parent.listaMoedas></table-informar-valor-laudo-relatorio-benfeitorias><table-estimar-valor-laudo-relatorio-benfeitorias ng-if=\"tipoValorLaudo == ESTIMAR_VALORES && listaBenfeitoriasImovel && listaBenfeitoriasImovel.length > 0 && tipoLaudo==LAUDO_RELATORIO\" nome-campo=$parent.nomeCampo benfeitorias-imovel=$parent.listaBenfeitoriasImovel detalhar-laudo-relatorio=$parent.detalharLaudoRelatorio redirecionar-informar-valor-cub=$parent.redirecionarInformarValorCub tipo-valor-laudo=$parent.tipoValorLaudo moeda=$parent.moeda lista-moedas=$parent.listaMoedas></table-estimar-valor-laudo-relatorio-benfeitorias><table-informar-valor-laudo-unidade-autonoma ng-if=\"tipoValorLaudo == INFORMAR_VALORES_LAUDO && unidadeAutonomaImovel && unidadeAutonomaImovel.length === 1 && unidadeAutonomaImove[0].areaConstruida\" nome-campo=$parent.nomeCampo unidade-autonoma-imovel=unidadeAutonomaImovel detalhar-laudo-relatorio=$parent.detalharLaudoRelatorio redirecionar-informar-valor-cub=$parent.redirecionarInformarValorCub tipo-valor-laudo=$parent.tipoValorLaudo valor-terreno=$parent.valorTerreno valor-adotado-selecionado=valorAdotadoSelecionado total-valor-terreno=$parent.totalValorTerreno moeda=$parent.moeda lista-moedas=$parent.listaMoedas></table-informar-valor-laudo-unidade-autonoma><table-estimar-valor-laudo-unidade-autonoma ng-if=\"tipoValorLaudo == ESTIMAR_VALORES && unidadeAutonomaImovel && unidadeAutonomaImovel.length === 1 && unidadeAutonomaImove[0].areaConstruida\" nome-campo=$parent.nomeCampo unidade-autonoma-imovel=unidadeAutonomaImovel detalhar-laudo-relatorio=$parent.detalharLaudoRelatorio redirecionar-informar-valor-cub=$parent.redirecionarInformarValorCub tipo-valor-laudo=$parent.tipoValorLaudo moeda=$parent.moeda lista-moedas=$parent.listaMoedas></table-estimar-valor-laudo-unidade-autonoma><table-estimar-valor-terreno-laudo-unidade-autonoma ng-if=\"tipoValorLaudo == ESTIMAR_VALORES_TERRENO && unidadeAutonomaImovel && unidadeAutonomaImovel.length === 1 && unidadeAutonomaImove[0].areaConstruida\" nome-campo=$parent.nomeCampo unidade-autonoma-imovel=unidadeAutonomaImovel detalhar-laudo-relatorio=$parent.detalharLaudoRelatorio redirecionar-informar-valor-cub=$parent.redirecionarInformarValorCub tipo-valor-laudo=$parent.tipoValorLaudo moeda=$parent.moeda lista-moedas=$parent.listaMoedas></table-estimar-valor-terreno-laudo-unidade-autonoma><table-estimar-valor-terreno-laudo-benfeitorias ng-if=\"tipoValorLaudo == ESTIMAR_VALORES_TERRENO && listaBenfeitoriasImovel && listaBenfeitoriasImovel.length > 0 && tipoLaudo==LAUDO_AVALIACAO\" nome-campo=$parent.nomeCampo benfeitorias-imovel=$parent.listaBenfeitoriasImovel detalhar-laudo-relatorio=$parent.detalharLaudoRelatorio redirecionar-informar-valor-cub=$parent.redirecionarInformarValorCub tipo-valor-laudo=$parent.tipoValorLaudo moeda=$parent.moeda lista-moedas=$parent.listaMoedas></table-estimar-valor-terreno-laudo-benfeitorias><table-estimar-valor-terreno-laudo-relatorio-benfeitorias ng-if=\"tipoValorLaudo == ESTIMAR_VALORES_TERRENO && listaBenfeitoriasImovel && listaBenfeitoriasImovel.length > 0 && tipoLaudo==LAUDO_RELATORIO\" nome-campo=$parent.nomeCampo benfeitorias-imovel=$parent.listaBenfeitoriasImovel detalhar-laudo-relatorio=$parent.detalharLaudoRelatorio redirecionar-informar-valor-cub=$parent.redirecionarInformarValorCub tipo-valor-laudo=$parent.tipoValorLaudo moeda=$parent.moeda lista-moedas=$parent.listaMoedas></table-estimar-valor-terreno-laudo-relatorio-benfeitorias><div class=\"m-t-20 m-b-20\" ng-if=\"tipoValorLaudo == ESTIMAR_VALORES\" layout=column flex=50><label class=\"sidenavTitulos marginTopHomologadorNome\" layout=row>{{\'label-valor-estimado-terreno\' | translate}}:</label><div layout=row>{{moeda ? moeda.simbolo : listaMoedas[0].simbolo}} {{(totalValorTerreno | currency).replace(\'R$\',\'\')}}<div class=prc-valor-estimado><span ng-bind=\"\'(\' +(((totalValorTerreno*100)/valorAdotadoSelecionado) | number:2) + \'% do valor do imóvel)\'\"></span></div></div></div></form>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/accordeonArquivoVistoria.tmpl.html","<form><div ng-if=\"(listaFotosImovel.length > 0) || !isVistoria\"><v-accordion multiple class=vAccordion--default><v-pane expanded=false><v-pane-header class=textoAccordionPvgLaudo><div flex=100 layout=row><div flex=30 layout=column flex-offset=5><img ng-src=\"{{gerarImagem(listaFotosImovel[0].imagem, listaFotosImovel[0].contentType)}}\" height=50 width=80 ng-show=\"listaFotosImovel.length > 0\"></div><div flex=70 layout=column flex-offset=5><label ng-bind=\"\'label-imovel-terreno\' | translate\"></label></div></div></v-pane-header><v-pane-content><inserir-imagem-imovel detalhar-laudo-relatorio=detalharLaudoRelatorio is-vistoria=isVistoria lista-fotos-imovel=listaFotosImovel nome-arquivo=\"\'Imóvel/Terreno\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></inserir-imagem-imovel></v-pane-content></v-pane></v-accordion></div><div ng-if=\"(listaBenfeitoria && listaBenfeitoria.length > 0 && listaFotosBenfeitoria.length > 0) || (listaBenfeitoria && listaBenfeitoria.length > 0 && !isVistoria)\"><v-accordion multiple class=vAccordion--default ng-repeat=\"benfeitoria in listaBenfeitoria\"><v-pane expanded=false><v-pane-header class=textoAccordionPvgLaudo><div flex=100 layout=row><div flex=30 layout=column flex-offset=5><img ng-src=\"{{gerarImagem(listaFotosBenfeitoria[benfeitoria.benfeitoriaImovel.id][0].imagem, listaFotosBenfeitoria[benfeitoria.benfeitoriaImovel.id][0].contentType)}}\" height=50 width=80 ng-show=\"listaFotosBenfeitoria.length > 0 && listaFotosBenfeitoria[benfeitoria.benfeitoriaImovel.id].length > 0\"></div><div flex=70 layout=column flex-offset=5><label ng-bind=\"(\'title-benfeitoria\' | translate) + \' \'\n                            + benfeitoria.benfeitoriaImovel.codigo + \' - \'\n                            + benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao + \'/\'\n                            + (benfeitoria.benfeitoriaImovel.edificacao ? benfeitoria.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></div></div></v-pane-header><v-pane-content><inserir-imagem-benfeitoria detalhar-laudo-relatorio=detalharLaudoRelatorio is-vistoria=isVistoria lista-fotos-benfeitoria=listaFotosBenfeitoria id-benfeitoria=benfeitoria.benfeitoriaImovel.id anexo-laudo-relatorio=anexoLaudoRelatorio nome-arquivo=\"benfeitoria.benfeitoriaImovel.codigo + \' - \' + benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao + \'/\'\n                            + (benfeitoria.benfeitoriaImovel.edificacao ? benfeitoria.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></inserir-imagem-benfeitoria></v-pane-content></v-pane></v-accordion></div><div ng-if=\"(unidadeAutonoma && listaFotosUnidadeAutonoma.length > 0) || (unidadeAutonoma && !isVistoria)\"><v-accordion multiple class=vAccordion--default><v-pane expanded=false><v-pane-header class=textoAccordionPvgLaudo><div flex=100 layout=row><div flex=30 layout=column flex-offset=5><img ng-src=\"{{gerarImagem(listaFotosUnidadeAutonoma[0].arquivo, listaFotosUnidadeAutonoma[0].contentType)}}\" height=50 width=80 ng-show=\"listaFotosUnidadeAutonoma.length > 0\"></div><div flex=70 layout=column flex-offset=5><label ng-bind=\"\'label-unidade-autonoma\' | translate\"></label></div></div></v-pane-header><v-pane-content><inserir-imagem-unidade-autonoma detalhar-laudo-relatorio=detalharLaudoRelatorio is-vistoria=isVistoria lista-fotos-unidade-autonoma=listaFotosUnidadeAutonoma nome-arquivo=\"\'Unidade Autônoma\'\" anexo-laudo-relatorio=anexoLaudoRelatorio></inserir-imagem-unidade-autonoma></v-pane-content></v-pane></v-accordion></div></form>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/documentosCartoriais.tmpl.html","<fieldset class=fieldsetArredondado layout=column layout-xs=column ng-show=\"listaCertidoesCartoriais.length > 0 || !detalharLaudoRelatorio\"><div layout=column class=fieldsetPaddingEstreito><div class=separarPaddingRows layout=row flex layout-align=\"start start\"><h3>{{\'title-documento-cartorial\' | translate}}</h3></div><div class=m-b-20 flex layout=row layout-align=end-end ng-show=!detalharLaudoRelatorio><md-button ng-show=mostrarBotaoCartorial class=\"md-primary md-raised\" ng-click=inserirNovoDocumentoCartoial(); style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-inserir-novo-documento-cartorial\' | translate}}</label></md-button></div><ng-form name=formularioDocumentosCartoriais layout=column ng-show=isDocumento><div layout=row layout-xs=column><md-input-container class=md-block flex=40 flex-xs=100 flex-sm=100><label>{{\'label-nome\' | translate}}:</label><input ng-model=documentoCartorial.nome type=text></md-input-container><md-input-container class=md-block flex=40 flex-xs=100 flex-sm=100><label>{{\'label-nome-cartorio\' | translate}}</label><md-select ng-model=documentoCartorial.cartorio><md-option ng-repeat=\"cartorio in listaCartorios\" ng-value=cartorio>{{cartorio.nome}}</md-option></md-select></md-input-container><div class=md-block flex-gt-xs flex=20><md-button style=\"margin: 0 0 0 15px; !important; width: calc(100% - 10px) !important\" class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=adicionarArquivoDocumentoCartorial($files)><label>{{customButtonLabel ? customButtonLabel : \'button-selecionar-arquivo\' | translate}}</label></md-button></div></div><div class=m-b-20 flex layout=row layout-align=end-end><md-button class=\"md-primary md-raised\" ng-click=adicionarListaDocumentosCartoriais() style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-adicionar-arquivo\' | translate}}</label></md-button></div></ng-form><div flex><div layout=column><md-content flex ng-show=\"listaCertidoesCartoriais.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 30%\" md-column>{{\'tr-nome\' | translate}}</th><th style=\"width: 40%\" md-column>{{\'tr-cartorio\' | translate}}</th><th style=\"width: 20%\" md-column>{{\'tr-arquivo\' | translate}}</th><th ng-show=!detalharLaudoRelatorio style=\"width: 10%\" md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documentoCartorial in paginationDocumentosCartoriais\"><td md-cell><label ng-bind=documentoCartorial.nome></label></td><td md-cell><label ng-bind=documentoCartorial.nomeCartorio></label></td><td md-cell><label ng-bind=documentoCartorial.arquivo.nome></label></td><td md-cell ng-show=!detalharLaudoRelatorio><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"removerDocumentoCartorial($index, documentoCartorial.id, documentoCartorial.arquivo.id);\" class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoDocumentosCartoriais md-page-select></md-table-pagination></md-content></div></div></div></fieldset>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/documentosLaudoRelatorioPadrao.tmpl.html","<ng-form name=formularioDocumentos><div class=upload-arquivos ng-show=\"!detalharLaudoAvaliacao || paginationDocumentos.length > 0\"><div layout=row layout-xs=column ng-if=titulo><h3 class=subtitulo-pagina>{{titulo}}<md-tooltip md-direction=top ng-if=hint>{{hint}}</md-tooltip></h3></div><div class=upload-body layout=row layout-xs=column ng-if=fileLimitValido() ng-show=!detalharLaudoAvaliacao><md-input-container class=md-block flex=\"{{campoData ? \'33\' : \'50\'}}\" flex-xs=100 ng-class=\"formEnviado && !documento.nome ? \'md-input-invalid\' : \'\'\"><label>{{customNomeLabel ? customNomeLabel : \'label-nome\' | translate}}*:</label><input ng-model=documento.nome type=text ng-disabled=detalharLaudoAvaliacao></md-input-container><md-container class=md-block flex=33 layout=column flex-xs flex-sm layout-xs=row layout-sm=row ng-show=campoData ng-class=\"formEnviado && !documento.data ? \'md-input-invalid\' : \'\'\"><md-datepicker ng-disabled=detalharLaudoAvaliacao md-max-date=dataAtual ng-model=documento.data md-placeholder=\"{{\'placeholder-data\' | translate}}*\" class=\"datePickerClass m-b-25\"></md-datepicker></md-container><div class=md-block flex-gt-xs flex=\"{{campoData ? \'33\' : \'50\'}}\"><md-button class=\"md-primary md-raised upload-button\" type=button ng-disabled=\"detalharLaudoAvaliacao || documento.arquivo.nome\" ngf-multiple=false ngf-select=adicionarArquivoDocumento($files)><label>{{customButtonLabel ? customButtonLabel : \'button-selecionar-arquivo\' | translate}}</label></md-button></div></div><div layout=row layout-xs=column ng-show=!detalharLaudoAvaliacao><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised add-button\" type=button ng-click=adicionarListaDocumentos(formularioDocumentos) ng-disabled=detalharLaudoAvaliacao><label>{{\'button-adicionar-arquivo\' | translate}}</label></md-button></div></div></div><div layout=row layout-xs=column class=upload-table ng-show=\"paginationDocumentos.length > 0\"><div flex><md-table-container><table md-table><tbody md-body><tr md-row ng-repeat=\"documentoLaudo in paginationDocumentos\"><td md-cell><label ng-bind=documentoLaudo.nome></label></td><td md-cell ng-show=campoData><label ng-bind=\"documentoLaudo.data | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><a ng-href={{baixarArquivo(documentoLaudo.arquivo.id)}} target=_blank><label class=mouse-pointer ng-bind=documentoLaudo.arquivo.nome></label></a></td><td md-cell ng-show=!detalharLaudoAvaliacao width=50px><a ng-click=\"removerDocumento($index, documentoLaudo.arquivo.id);\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'label-excluir-documento\' | translate}}\"><md-tooltip>{{\'label-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></div></div></div></ng-form>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/enquadramentoLaudoSegundoGrauFundamentacao.tmpl.html","<div layout=row flex flex-xs><label class=textoAccordionPvgLaudo>{{\'title-fundamentacao-nbr\' | translate}}</label></div><div flex><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row flex=100><th width=10% md-column>{{\'title-item\' | translate}}</th><th width=25% md-column>{{\'title-descricao\' | translate}}</th><th width=20% md-column>{{\'title-grau-3\' | translate}}</th><th width=20% md-column>{{\'title-grau-2\' | translate}}</th><th width=20% md-column>{{\'title-grau-1\' | translate}}</th><th width=5% md-column>{{\'title-pontos-obtidos\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>1</label></td><td md-cell><label>{{\'label-fundamentacao-01-descricao-caracterizacao-imovel\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-01-grau-complementa-variaveis-analisadas\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-01-grau-complementa-variaveis-utilizadas\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-01-grau-adocao-situacao-paradgma\' | translate}}</label></td><td md-cell ng-show=\"!isAssinar && !detalharLaudoRelatorio\"><md-input-container class=md-block flex flex-xs flex-sm><label>{{\'label-selecione\' | translate}}</label><md-select ng-disable=detalharLaudoRelatorio ng-model=avaliacao.enquadramento.caracterizacaoImovelAvaliado required placeholder=\"{{\'label-selecione\' | translate}}\" ng-change=contarPontosObtidosFundamentacao();medirGrauEnquadramentoFundamentacao(); ng-disabled=\"isAssinar || detalharLaudoRelatorio\"><md-option ng-repeat=\"nivelFundamento in listaNivelFundamentacao\" ng-value=nivelFundamento.name class=item1>{{nivelFundamento.nivel}}</md-option></md-select></md-input-container></td><td md-cell ng-show=\"isAssinar || detalharLaudoRelatorio\"><label ng-bind=labelcaracterizacaoImovelAvaliado></label></td></tr><tr md-row><td md-cell><label>2</label></td><td md-cell><label>{{\'label-fundamentacao-02-descricao-quantidade-minima-dados\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-02-grau-6-numero-variaveis-independentes\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-02-grau-4-numero-variaveis-independentes\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-02-grau-3-numero-variaveis-independentes\' | translate}}</label></td><td md-cell ng-show=\"!isAssinar && !detalharLaudoRelatorio\"><md-input-container class=md-block flex flex-xs flex-sm><label>{{\'label-selecione\' | translate}}</label><md-select ng-model=avaliacao.enquadramento.quantidadeMinimaDados required placeholder=\"{{\'label-selecione\' | translate}}\" ng-change=contarPontosObtidosFundamentacao();medirGrauEnquadramentoFundamentacao(); ng-disabled=\"isAssinar || detalharLaudoRelatorio\"><md-option ng-repeat=\"nivelFundamento in listaNivelFundamentacao\" ng-value=nivelFundamento.name class=item2>{{nivelFundamento.nivel}}</md-option></md-select></md-input-container></td><td md-cell ng-show=\"isAssinar || detalharLaudoRelatorio\"><label ng-bind=labelquantidadeMinimaDados></label></td></tr><tr md-row><td md-cell><label>3</label></td><td md-cell><label>{{\'label-fundamentacao-03-descricao-identificacao-dados-mercado\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-03-grau-apresentacao-informacoes-relativas-caracteristicas-conferidas\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-03-grau-apresentacao-informacoes-relativas\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-03-grau-apresentacao-informacoes-utilizadas-modelo\' | translate}}</label></td><td md-cell ng-show=\"!isAssinar && !detalharLaudoRelatorio\"><md-input-container class=md-block flex flex-xs flex-sm><label>{{\'label-selecione\' | translate}}</label><md-select ng-model=avaliacao.enquadramento.identificacaoDadosMercado required placeholder=\"{{\'label-selecione\' | translate}}\" ng-change=contarPontosObtidosFundamentacao();medirGrauEnquadramentoFundamentacao(); ng-disabled=\"isAssinar || detalharLaudoRelatorio\"><md-option ng-repeat=\"nivelFundamento in listaNivelFundamentacao\" ng-value=nivelFundamento.name class=item3>{{nivelFundamento.nivel}}</md-option></md-select></md-input-container></td><td md-cell ng-show=\"isAssinar || detalharLaudoRelatorio\"><label ng-bind=labelidentificacaoDadosMercado></label></td></tr><tr md-row><td md-cell><label>4</label></td><td md-cell><label>{{\'label-fundamentacao-04-descricao-extrapolacao\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-04-grau-nao-admitida\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-04-admitida-apenas-referida-variavel\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-04-admitida-apenas-referidas-variaveis-simutaneamente-modulo\' | translate}}</label></td><td md-cell ng-show=\"!isAssinar && !detalharLaudoRelatorio\"><md-input-container class=\"md-block espacamentoInputs\" flex flex-xs flex-sm><label>{{\'label-selecione\' | translate}}</label><md-select ng-model=avaliacao.enquadramento.extrapolacao required placeholder=\"{{\'label-selecione\' | translate}}\" ng-change=contarPontosObtidosFundamentacao();medirGrauEnquadramentoFundamentacao(); ng-disabled=\"isAssinar || detalharLaudoRelatorio\"><md-option ng-repeat=\"nivelFundamento in listaNivelFundamentacao\" ng-value=nivelFundamento.name class=item4>{{nivelFundamento.nivel}}</md-option></md-select></md-input-container></td><td md-cell ng-show=\"isAssinar || detalharLaudoRelatorio\"><label ng-bind=labelextrapolacao></label></td></tr><tr md-row><td md-cell><label>5</label></td><td md-cell><label>{{\'label-fundamentacao-05-nivel-significancia-teste-bicaldal\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-05-grau-dez-porcento\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-05-grau-vinte-porcento\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-05-grau-trinta-porcento\' | translate}}</label></td><td md-cell ng-show=\"!isAssinar && !detalharLaudoRelatorio\"><md-input-container class=\"md-block espacamentoInputs\" flex flex-xs flex-sm><label>{{\'label-selecione\' | translate}}</label><md-select ng-model=avaliacao.enquadramento.nivelSignificanciaMaximoRejeicao required placeholder=\"{{\'label-selecione\' | translate}}\" ng-change=contarPontosObtidosFundamentacao();medirGrauEnquadramentoFundamentacao(); ng-disabled=\"isAssinar || detalharLaudoRelatorio\"><md-option ng-repeat=\"nivelFundamento in listaNivelFundamentacao\" ng-value=nivelFundamento.name class=item5>{{nivelFundamento.nivel}}</md-option></md-select></md-input-container></td><td md-cell ng-show=\"isAssinar || detalharLaudoRelatorio\"><label ng-bind=labelnivelSignificanciaMaximoRejeicao></label></td></tr><tr md-row><td md-cell><label>6</label></td><td md-cell><label>{{\'label-fundamentacao-06-nivel-significancia-teste-snedecor\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-06-um-porcento\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-dois-porcento\' | translate}}</label></td><td md-cell><label>{{\'label-fundamentacao-cinco-porcento\' | translate}}</label></td><td md-cell ng-show=\"!isAssinar && !detalharLaudoRelatorio\"><md-input-container class=\"md-block espacamentoInputs\" flex flex-xs flex-sm><label>{{\'label-selecione\' | translate}}</label><md-select ng-model=avaliacao.enquadramento.nivelSignificanciaMaximoAdmitido required placeholder=\"{{\'label-selecione\' | translate}}\" ng-change=contarPontosObtidosFundamentacao();medirGrauEnquadramentoFundamentacao(); ng-disabled=\"isAssinar || detalharLaudoRelatorio\"><md-option ng-repeat=\"nivelFundamento in listaNivelFundamentacao\" ng-value=nivelFundamento.name class=item6>{{nivelFundamento.nivel}}</md-option></md-select></md-input-container></td><td md-cell ng-show=\"isAssinar || detalharLaudoRelatorio\"><label ng-bind=labelnivelSignificanciaMaximoAdmitido></label></td></tr></tbody></table></md-table-container></md-content></div></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'title-pontuacao-obtida\' | translate}}:</label>&nbsp;<label ng-bind=somatorioPontosObtidos></label></div><br><div layout-xs=row flex flex-xs flex-sm ng-if=\"labelPontosObtidos && labelPontosObtidos != \'\' && !isAssinar\"><md-input-container layout-xs=row flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><label>{{\'title-justificativa-nao-atingir-grau\' | translate}}: {{labelPontosObtidos}}</label><textarea ng-disabled=detalharLaudoRelatorio class=barraRolagemTextArea ng-model=avaliacao.enquadramento.justificativaNaoAtingimento ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-justificativa-nao-atingir-grau\' | translate}}\" ng-required=\"labelPontosObtidos && labelPontosObtidos != \'\'\"></textarea></md-input-container></div><div layout=row flex flex-xs><label class=textoAccordionPvgLaudo>{{\'title-enquadrament-laudo-segundo-grau\' | translate}}</label></div><div flex class=marginTopHomologadorNome><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'title-graus\' | translate}}</th><th md-column>III</th><th md-column>II</th><th md-column>I</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>{{\'label-enquadramento-laudo-fundamentacao-pontos-minimos\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrau3 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-enquadramento-laudo-fundamentacao-dezesseis\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrau2 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-enquadramento-laudo-fundamentacao-dez\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrau1 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-enquadramento-laudo-fundamentacao-seis\' | translate}}</label></td></tr><tr md-row><td md-cell><label>{{\'label-enquadramento-laudo-fundamentacao-itens-obrigatorios\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrau3 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-enquadramento-laudo-fundamentacao-grau-iii-demais-minimo-grau-ii\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrau2 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-enquadramento-laudo-fundamentacao-grau-ii-demais-minimo-grau-i\' | translate}}</label></td><td md-cell ng-style=\"enquadramentoGrau1 ? colorTableColumnAvaliacao : {}\"><label>{{\'label-enquadramento-laudo-fundamentacao-todos-minimo-grau-i\' | translate}}</label></td></tr></tbody></table></md-table-container></md-content></div></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'title-grau-fundamentacao-laudo\' | translate}}:</label>&nbsp;<label ng-show=enquadramentoGrau3>{{\'title-grau-3\' | translate}}</label><label ng-show=enquadramentoGrau2>{{\'title-grau-2\' | translate}}</label><label ng-show=enquadramentoGrau1>{{\'title-grau-1\' | translate}}</label><label style=\"color: red\" ng-show=enquadramentoGrauNaoAtingido>{{\'label-laudo-nao-possui-enquadramento-grau-fundamentacao\' | translate}}</label></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/enquadramentoLaudoSegundoGrauPrecisao.tmpl.html","<div layout=row flex flex-xs><label class=\"textoAccordionPvgLaudo marginTop10\">{{\'title-enquadramento-laudo-segundo-grau-precisao\' | translate}}:</label></div><div layout=column flex flex-xs ng-if=!isAssinar><label>{{\'title-amplitude-intervalo-confianca\' | translate}}:</label><div flex=30 layout=row><md-input-container flex=30 layout-xs=row flex-xs flex-sm class=md-block layout-xs=column layout-sm=column><input disabled name=\"{{\'title-amplitude-intervalo-confianca\' | translate}}\" ng-model=avaliacao.enquadramento.amplitudeIntervalo ui-number-mask=2 max=100.00 maxlength=6></md-input-container><div flex=10 layout=r><label class=marginTop3>%</label></div></div></div><div flex class=marginTopHomologadorNome><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'title-descricao\' | translate}}</th><th md-column>{{\'title-grau-3\' | translate}}</th><th md-column>{{\'title-grau-2\' | translate}}</th><th md-column>{{\'title-grau-1\' | translate}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label>{{\'label-enquadramento-laudo-precisao-amplitude-intervalo-confianca\' | translate}}</label></td><td md-cell ng-style=\"avaliacao.enquadramento.amplitudeIntervalo <= 30 ? colorTableColumnAvaliacao : {}\"><label data-ng-bind=\"\'<= 30%\'\"></label></td><td md-cell ng-style=\"avaliacao.enquadramento.amplitudeIntervalo > 30 && avaliacao.enquadramento.amplitudeIntervalo <= 40 ? colorTableColumnAvaliacao : {}\"><label data-ng-bind=\"\'<= 40%\'\"></label></td><td md-cell ng-style=\"avaliacao.enquadramento.amplitudeIntervalo > 40 && avaliacao.enquadramento.amplitudeIntervalo <= 50 ? colorTableColumnAvaliacao : {}\"><label data-ng-bind=\"\'<= 50%\'\"></label></td></tr></tbody></table></md-table-container></md-content></div></div><div class=pdfEspacamentoLabels><label class=negritoTexto>{{\'title-grau-precisao-laudo-avaliacao\' | translate}}:</label>&nbsp;<label ng-show=\"avaliacao.enquadramento.amplitudeIntervalo <= 30\">{{\'title-grau-3\' | translate}}</label><label ng-show=\"avaliacao.enquadramento.amplitudeIntervalo > 30 && avaliacao.enquadramento.amplitudeIntervalo <= 40\">{{\'title-grau-2\' | translate}}</label><label ng-show=\"avaliacao.enquadramento.amplitudeIntervalo > 40 && avaliacao.enquadramento.amplitudeIntervalo <= 50\">{{\'title-grau-1\' | translate}}</label><label style=\"color: red\" ng-show=\"avaliacao.enquadramento.amplitudeIntervalo > 50\">{{\'label-laudo-nao-possui-enquadramento-resultado-precisao\' | translate}}</label></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/inserirImagemBenfeitoria.tmpl.html","<div ng-if=\"!isVistoria && !detalharLaudoRelatorio\"><div layout=row class=paddingVertical20><md-actions class=\"md-block sizeSelecionarArquivo paddingLateral20\" flex=50><md-button class=\"semMargem minimaLargura btn-size-big btn-avaliacao\" ngf-multiple=false ngf-select=adicionarFoto($files)><label>{{\'button-selecionar-arquivo\' | translate}}</label></md-button></md-actions><md-input-container class=\"md-block semMargem\" flex=50 flex-xs=100 flex-offset=5><label>{{\'label-nome\' | translate}}</label><input ng-model=$parent.descricaoFoto name=\"{{\'label-nome\' | translate}}\"></md-input-container></div><div class=\"classDistanciamentoLabel paddingVertical20\"><md-button class=\"semMargem btn-size-big btn-avaliacao\" ng-click=adicionarListaFotos()><label>{{\'button-adicionar\' | translate}}</label></md-button></div></div><div flex class=caixa-tabela-resultado><div layout=column><md-content flex ng-show=\"listaFotosBenfeitoria.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th ng-if=isVistoria style=\"width: 14%\" class=th-margin-pvgs md-column>{{\'tr-fotos-selecionadas\' | translate}}</th><th style=\"width: 14%\" class=th-margin-pvgs md-column>{{\'tr-arquivo\' | translate}}</th><th style=\"width: 9%\" class=th-margin-pvgs md-column>{{\'tr-nome\' | translate}}</th><th style=\"width: 16%\" class=th-margin-pvgs md-column>{{\'tr-data\' | translate}}</th><th ng-if=\"!isVistoria && !detalharLaudoRelatorio\" style=\"width: 8%\" class=th-margin-pvgs md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"dadosFoto in paginacaoFotos\"><td ng-if=isVistoria class=th-margin-pvgs md-cell><div layout=row layout-align=\"center center\"><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=dadosFoto.isSelecionada></md-checkbox></div></td><td class=th-margin-pvgs md-cell><a ng-href={{baixarImagem(dadosFoto.id)}} target=_blank>{{dadosFoto.nomeArquivo}}</a></td><td class=th-margin-pvgs md-cell>{{dadosFoto.descricao}}</td><td class=th-margin-pvgs md-cell ng-bind=\"(dadosFoto.dataEmissao | date: \'dd/MM/yyyy\' : \'-0300\')\"></td><td md-cell ng-if=\"!isVistoria && !detalharLaudoRelatorio\"><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"removerFotoImovel($index, dadosFoto.id)\" class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir\' | translate}}\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginarListagem md-page-select></md-table-pagination></md-content></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/inserirImagemImovel.tmpl.html","<div ng-if=\"!isVistoria && !detalharLaudoRelatorio\"><div layout=row class=paddingVertical20><md-actions class=\"md-block sizeSelecionarArquivo paddingLateral20\" flex=50><md-button class=\"semMargem minimaLargura btn-size-big btn-avaliacao\" ngf-multiple=false ngf-select=adicionarFoto($files)><label>{{\'button-selecionar-arquivo\' | translate}}</label></md-button></md-actions><md-input-container class=\"md-block semMargem\" flex=50 flex-xs=100 flex-offset=5><label>{{\'label-nome\' | translate}}</label><input ng-model=$parent.descricaoFoto name=\"{{\'label-nome\' | translate}}\"></md-input-container></div><div class=\"classDistanciamentoLabel paddingVertical20\"><md-button class=\"semMargem btn-size-big btn-avaliacao\" ng-click=adicionarListaFotos()><label>{{\'button-adicionar\' | translate}}</label></md-button></div></div><div flex class=caixa-tabela-resultado><div layout=column><md-content flex ng-show=\"listaFotosImovel.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th ng-if=isVistoria style=\"width: 14%\" class=th-margin-pvgs md-column>{{\'tr-fotos-selecionadas\' | translate}}</th><th style=\"width: 14%\" class=th-margin-pvgs md-column>{{\'tr-arquivo\' | translate}}</th><th style=\"width: 9%\" class=th-margin-pvgs md-column>{{\'tr-nome\' | translate}}</th><th style=\"width: 16%\" class=th-margin-pvgs md-column>{{\'tr-data\' | translate}}</th><th ng-if=\"!isVistoria && !detalharLaudoRelatorio\" style=\"width: 8%\" class=th-margin-pvgs md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"dadosFoto in paginacaoFotosImovel\"><td ng-if=isVistoria class=th-margin-pvgs md-cell><div layout=row layout-align=\"center center\"><md-checkbox ng-disabled=detalharLaudoRelatorio class=md-primary ng-model=dadosFoto.isSelecionada></md-checkbox></div></td><td class=th-margin-pvgs md-cell><a ng-href={{baixarImagem(dadosFoto.id)}} target=_blank>{{dadosFoto.nomeImagem}}</a></td><td class=th-margin-pvgs md-cell>{{dadosFoto.descricao}}</td><td class=th-margin-pvgs md-cell ng-bind=\"(dadosFoto.data | date: \'dd/MM/yyyy\' : \'-0300\')\"></td><td md-cell ng-if=\"!isVistoria && !detalharLaudoRelatorio\"><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"removerFotoImovel($index, dadosFoto.id)\" class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir\' | translate}}\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginarListagemImovel md-page-select></md-table-pagination></md-content></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/inserirImagemUnidadeAutonoma.tmpl.html","<div ng-if=\"!isVistoria && !detalharLaudoRelatorio\"><div layout=row class=paddingVertical20><md-actions class=\"md-block sizeSelecionarArquivo paddingLateral20\" flex=50><md-button class=\"semMargem minimaLargura btn-size-big btn-avaliacao\" ngf-multiple=false ngf-select=adicionarFoto($files)><label>{{\'button-selecionar-arquivo\' | translate}}</label></md-button></md-actions><md-input-container class=\"md-block semMargem\" flex=50 flex-xs=100 flex-offset=5><label>{{\'label-nome\' | translate}}</label><input ng-model=$parent.descricaoFoto name=\"{{\'label-nome\' | translate}}\"></md-input-container></div><div class=\"classDistanciamentoLabel paddingVertical20\"><md-button class=\"semMargem btn-size-big btn-avaliacao\" ng-click=adicionarListaFotos()><label>{{\'button-adicionar\' | translate}}</label></md-button></div></div><div flex class=caixa-tabela-resultado><div layout=column><md-content flex ng-show=\"listaFotosUnidadeAutonoma.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th ng-if=isVistoria style=\"width: 14%\" class=th-margin-pvgs md-column>{{\'tr-fotos-selecionadas\' | translate}}</th><th style=\"width: 14%\" class=th-margin-pvgs md-column>{{\'tr-arquivo\' | translate}}</th><th style=\"width: 9%\" class=th-margin-pvgs md-column>{{\'tr-nome\' | translate}}</th><th style=\"width: 16%\" class=th-margin-pvgs md-column>{{\'tr-data\' | translate}}</th><th ng-if=\"!isVistoria && !detalharLaudoRelatorio\" style=\"width: 8%\" class=th-margin-pvgs md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"dadosFoto in paginacaoFotos\"><td ng-if=isVistoria class=th-margin-pvgs md-cell><div layout=row layout-align=\"center center\"><md-checkbox class=md-primary ng-model=dadosFoto.isSelecionada></md-checkbox></div></td><td class=th-margin-pvgs md-cell><a ng-href={{baixarImagem(dadosFoto.id)}} target=_blank>{{dadosFoto.nomeGerado}}</a></td><td class=th-margin-pvgs md-cell>{{dadosFoto.descricao}}</td><td class=th-margin-pvgs md-cell ng-bind=\"(dadosFoto.data | date: \'dd/MM/yyyy\' : \'-0300\')\"></td><td md-cell ng-if=\"!isVistoria && !detalharLaudoRelatorio\"><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=\"removerFotoImovel($index, dadosFoto.id)\" class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir\' | translate}}\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginarListagem md-page-select></md-table-pagination></md-content></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/matriculaTranscricao.tmpl.html","<fieldset class=fieldsetArredondado layout=column layout-xs=column ng-show=\"listaMatriculasTranscricao.length > 0 || !detalharLaudoRelatorio\"><div layout=column class=fieldsetPaddingEstreito><div class=separarPaddingRows layout=row flex layout-align=\"start start\"><h3>{{\'title-matricula-transcricao\' | translate}}</h3></div><div flex layout=row layout-align=end-end ng-show=!detalharLaudoRelatorio><md-button ng-show=isMostrarBotaoMatricula class=\"md-primary md-raised\" ng-click=inserirNovaMatriculaTranscricao(); style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-inserir-nova-matricula-transcricao\' | translate}}</label></md-button></div><div id=formData><br><ng-form name=formularioMatriculas layout=column layout-xs=column ng-show=isMatricula><div layout=row layout-xs=column flex-sm=column><md-input-container class=md-block flex=30 flex-xs=100 flex-sm=100><label>{{\'label-uf\' | translate}}</label><md-select ng-model=matriculaTranscricao.uf ng-change=pesquisarMunicipiosPorUf();pesquisarCartoriosPorUfIntegrador(); ng-required=isMatricula><md-option ng-repeat=\"uf in listaUFs | orderBy: \'sigla\'\" ng-value=uf.sigla ng-selected=\"imovel.enderecamento.uf==uf.sigla\">{{uf.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=30 flex-xs=100 flex-sm=100><label>{{\'label-municipio\' | translate}}</label><md-select ng-model=matriculaTranscricao.municipio ng-change=pesquisarCartoriosPorUfMunicipioIntegrador(); ng-required=isMatricula ng-selected=\"imovel.enderecamento.municipio==municipio.nome\"><md-option ng-repeat=\"municipio in listaMunicipios | orderBy: \'nome\'\" ng-value=municipio>{{municipio.nome}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=40 flex-xs=100 flex-sm=100 ng-show=\"imovel.enderecamento.pais==BRASIL\"><label>{{\'label-nome-cartorio\' | translate}}</label><md-select ng-model=matriculaTranscricao.cartorio ng-required=\"isMatricula &&  imovel.enderecamento.pais==BRASIL\"><md-option ng-repeat=\"cartorio in listaCartorios\" ng-value=cartorio>{{cartorio.nome}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column flex-sm=column ng-show=\"imovel.enderecamento.pais!=BRASIL\"><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><label>{{\'label-identificacao-cartorio\' | translate}}</label><textarea rows=1 cols=50 name=\"{{\'label-identificacao-cartorio\' | translate}}\" ng-model=matriculaTranscricao.cartorioEstrangeiro></textarea></md-input-container></div><div layout=row layout-xs=column flex=100 flex-xs=100><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><label>{{\'label-matricula-transcrição\' | translate}}</label><input ng-model=matriculaTranscricao.numero ng-required=isMatricula></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><md-datepicker ng-model=matriculaTranscricao.dataMatricula md-max-date=hoje name=\"{{\'placeholder-data-matricula\' | translate}}\" md-placeholder=\"{{\'placeholder-data-matricula\' | translate}}\"></md-datepicker></md-input-container><md-input-container class=md-block flex=33 flex-xs=100 flex-sm=100><md-datepicker ng-model=matriculaTranscricao.dataRegistro md-max-date=hoje name=\"{{\'placeholder-data-registro\' | translate}}\" md-placeholder=\"{{\'placeholder-data-registro\' | translate}}\"></md-datepicker></md-input-container></div><div class=\"m-t-20 m-b-20\" flex layout=row layout-align=end-end><md-button ng-show=!editar.buttonShow class=\"md-primary md-raised\" ng-click=adicionarMatriculaTranscricao(formularioMatriculas) style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-adicionar\' | translate}}</label></md-button><md-button ng-show=editar.buttonShow class=\"md-primary md-raised\" ng-click=gravarAlteracaoMatriculaTranscricao() style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-gravar\' | translate}}</label></md-button></div></ng-form></div><div flex><div layout=column><md-content flex ng-show=\"listaMatriculasTranscricao.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 10%\" md-column>{{\'tr-localizacao\' | translate}}</th><th style=\"width: 30%\" md-column>{{\'tr-cartorio\' | translate}}</th><th style=\"width: 20%\" md-column>{{\'tr-matricula-transcricao\' | translate}}</th><th style=\"width: 20%\" md-column>{{\'tr-data-matricula\' | translate}}</th><th style=\"width: 20%\" md-column>{{\'tr-data-registro\' | translate}}</th><th ng-show=!detalharLaudoRelatorio style=\"width: 20%\" md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"matricula in paginationMatriculaTranscricao\"><td md-cell><label ng-bind=\"matricula.uf + \'/\' + matricula.municipio\"></label></td><td md-cell><label ng-bind=matricula.nomeCartorio></label></td><td md-cell><label ng-bind=matricula.numero></label></td><td md-cell><label ng-bind=\"matricula.dataMatricula | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell><label ng-bind=\"matricula.dataRegistro | date: \'dd/MM/yyyy\' : \'-0300\'\"></label></td><td md-cell ng-show=!detalharLaudoRelatorio><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=excluirMatriculaTranscricao($index); ng-if=!responsavelTecnico.usuarioLogado class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir\' | translate}}\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoMatriculasTranscricao md-page-select></md-table-pagination></md-content></div></div></div></fieldset>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/responsavelTecnico.tmpl.html","<fieldset class=fieldsetArredondado layout=column layout-xs=column><div layout=row flex layout-align=\"start start\" class=m-t-20><h3 class=subtitulo-pagina>{{customTitle ? customTitle : \'label-responsavel-tecnico\' | translate}}<md-tooltip md-direction=right ng-if=exibirHint>{{descricaoHint}}</md-tooltip></h3></div><div layout=column flex=100 layout-align=\"start start\" class=\"m-b-10 alerta-obrigatorio-responsavel-tecnico\"><div layout=row layout-align=\"start center\"><md-icon class=icone-alerta-amarelo>report_problem</md-icon><span ng-bind-html=\"\'label-alerta-obrigatorio-responsavel-tecnico\' | translate\"></span></div></div><div layout=row layout-xs=column ng-show=!detalharLaudoRelatorio><div class=md-block flex-gt-xs flex=40></div><div class=md-block flex-gt-xs flex=60><label class=datepicker-label><label>{{\'label-registro-profissional\' | translate}}*</label></label></div></div><div layout=row ng-show=!detalharLaudoRelatorio><md-input-container flex=40><label>{{\'label-cpf\' | translate}}*:</label><input type=text ng-model=responsavel.cpf ui-br-cpf-mask name=\"{{nomeCampo}} - {{\'label-cpf\' | translate}}\" ng-blur=verificarCpfAoDigitar()></md-input-container><md-radio-group flex=20 class=tamanho-radio-button-avaliacao ng-model=responsavel.registroProfissional ng-change=changeRegistroProfissional() name=\"{{nomeCampo}} - {{\'label-registro-profissional\' | translate}}\"><div layout=row><md-radio-button ng-repeat=\"(k,v) in listaCreaCrau\" layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=k><div class=texto-preto>{{v}}</div></md-radio-button></div></md-radio-group><md-input-container flex=40 ng-show=responsavel.registroProfissional><label ng-show=\"responsavel.registroProfissional == \'CREA\'\">{{\'label-crea\' | translate}}*:</label><label ng-show=\"responsavel.registroProfissional == \'CAU\'\">{{\'label-cau\' | translate}}*:</label><input ng-if=responsavel.registroProfissional type=text ng-model=responsavel.crea md-maxlength=15 maxlength=15 name=\"{{nomeCampo}} - {{\'label-crea-cau\' | translate}}\"></md-input-container></div><div style=\"margin-top: -20px; z-index: 9999\" layout=row layout-xs=column ng-show=!detalharLaudoRelatorio><div class=md-block flex-gt-xs flex=40><label>{{pessoa.nome}}</label></div><div class=md-block flex-gt-xs flex=60></div></div><div class=m-t-20 flex layout=row layout-align=end-end><md-button ng-show=\"!detalharLaudoRelatorio && !editar.buttonShow\" class=\"md-primary md-raised\" id=buttonAdicionarResponsavel ng-click=adicionarResponsavelTecnico() style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-adicionar\' | translate}}</label></md-button><md-button ng-show=\"!detalharLaudoRelatorio && editar.buttonShow\" class=\"md-primary md-raised\" id=buttonEditarResponsavel ng-click=gravarAlteracaoResponsavelTecnico() style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-gravar\' | translate}}</label></md-button></div><div flex class=caixa-tabela-resultado ng-show=\"listaResponsaveisTecnicos.length > 0\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 40%\" class=th-margin-pvgs md-column>{{\'label-nome\' | translate}}</th><th style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-registro-profissional\' | translate}}</th><th ng-if=assinaturaNotaTecnica style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-assinatura-nota-tecnica\' | translate}}</th><th ng-if=assinaturaRevalidacao style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-assinatura-revalidacao\' | translate}}</th><th style=\"width: 10%\" ng-show=!detalharLaudoRelatorio class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"responsavelTecnico in paginationResponsaveisTecnico\"><td md-cell class=th-margin-pvgs ng-bind=responsavelTecnico.nome></td><td md-cell class=th-margin-pvgs ng-style=\"responsavelTecnico.crea ? {} : labelVermelho\">{{responsavelTecnico.crea ? responsavelTecnico.registroProfissional + \' - \' + responsavelTecnico.crea : \'pendente\'}}</td><td ng-if=\"assinaturaNotaTecnica || assinaturaRevalidacao\" md-cell class=th-margin-pvgs ng-style=\"responsavelTecnico.dataAssinatura ? {} : labelVermelho\">{{responsavelTecnico.dataAssinatura ? (responsavelTecnico.dataAssinatura | date:\'dd/MM/yyyy\') + \' \' + (\'label-as\' | translate) + \' \' + (responsavelTecnico.dataAssinatura | date:\'HH:mm:ss\') : \'pendente\'}}</td><td md-cell ng-show=!detalharLaudoRelatorio><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=alterarResponsavelTecnico($index) ng-show=!detalharLaudoRelatorio class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-editar-responsavel-tecnico\' | translate}}\" ng-bind=\"\'edit\'\" aria-label=\"{{\'label-editar-responsavel-tecnico\' | translate}}\"><md-tooltip>{{\'label-editar-responsavel-tecnico\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=removerResponsavelTecnico($index) ng-if=!responsavelTecnico.usuarioLogado ng-show=!detalharLaudoRelatorio class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir-responsavel-tecnico\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir-responsavel-tecnico\' | translate}}\"><md-tooltip>{{\'label-excluir-responsavel-tecnico\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoResponsaveisTecnicos md-page-select></md-table-pagination></md-content></div></div></fieldset>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/responsavelTecnico.tmpl.view.html","<fieldset class=fieldsetArredondado layout=column layout-xs=column><div layout=row flex layout-align=\"start start\" class=m-t-20><h3 class=subtitulo-pagina>{{customTitle ? customTitle : \'label-responsavel-tecnico\' | translate}}<md-tooltip md-direction=right ng-if=exibirHint>{{descricaoHint}}</md-tooltip></h3></div><div flex ng-show=\"listaResponsaveisTecnicos.length > 0\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 40%\" class=th-margin-pvgs md-column>{{\'label-nome\' | translate}}</th><th style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-registro-profissional\' | translate}}</th><th ng-if=assinaturaNotaTecnica style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-assinatura-nota-tecnica\' | translate}}</th><th ng-if=assinaturaRevalidacao style=\"width: 25%\" class=th-margin-pvgs md-column>{{\'label-assinatura-revalidacao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"responsavelTecnico in paginationResponsaveisTecnico\"><td md-cell class=th-margin-pvgs ng-bind=responsavelTecnico.nome></td><td md-cell class=th-margin-pvgs ng-style=\"responsavelTecnico.crea ? {} : labelVermelho\">{{responsavelTecnico.crea ? responsavelTecnico.registroProfissional + \' - \' + responsavelTecnico.crea : \'pendente\'}}</td><td ng-if=\"assinaturaNotaTecnica || assinaturaRevalidacao\" md-cell class=th-margin-pvgs ng-style=\"responsavelTecnico.dataAssinatura ? {} : labelVermelho\">{{responsavelTecnico.dataAssinatura ? (responsavelTecnico.dataAssinatura | date:\'dd/MM/yyyy\') + \' \' + (\'label-as\' | translate) + \' \' + (responsavelTecnico.dataAssinatura | date:\'HH:mm:ss\') : \'pendente\'}}</td></tr></tbody></table></md-table-container></md-content></div></div></fieldset>");
$templateCache.put("scripts-avaliacao/app/directives/secoes-laudo-relatorio/templates/variaveisAmostrasLaudoRelatorio.tmpl.html","<fieldset class=fieldsetArredondado layout=column layout-xs=column><div layout=row flex layout-align=\"start start\" class=m-t-20><h3 class=subtitulo-pagina>{{\'label-variaveis\' | translate}}</h3></div><div layout=row layout-xs=column layout-sm=column><md-input-container class=md-block flex=\"{{variavel.nome == \'OUTRO\' ? \'33\' : \'50\'}}\" flex-xs layout-xs=row layout-sm=row><label>{{\'label-variavel\' | translate}}:*</label><md-select ng-disabled=detalharLaudoRelatorio ng-model=variavel.variavel ng-change=changeVariavel() name=\"{{nomeCampo}} - {{\'label-variavel\' | translate}}\" placeholder=\"{{\'label-variavel\' | translate}}:*\"><md-option ng-repeat=\"(k,v) in listaVariaveis\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=\"{{variavel.nome == \'OUTRO\' ? \'33\' : \'50\'}}\" flex-xs layout-xs=row layout-sm=row><label>{{\'label-nome\' | translate}}:*</label><md-select ng-disabled=\"detalharLaudoRelatorio || !variavel.variavel\" ng-model=variavel.nome name=\"{{nomeCampo}} - {{\'label-nome\' | translate}}\" placeholder=\"{{\'label-nome\' | translate}}:*\"><md-option ng-repeat=\"(k,v) in listaNomesVariavel\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-input-container ng-show=\"variavel.nome == \'OUTRO\'\" flex=33><label>{{\'label-nome-outra-variavel\' | translate}}:</label><input ng-disabled=detalharLaudoRelatorio type=text ng-required=\"variavel.nome == \'OUTRO\'\" ng-model=variavel.nomeOutraVariavel name=\"{{nomeCampo}} - {{\'label-nome\' | translate}}\" maxlength=50 ng-maxlength=50></md-input-container></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50></div><div class=md-block flex-gt-xs flex=50><label class=datepicker-label><label>{{\'label-variavel-utilizada-modelo\' | translate}}*</label></label></div></div><div layout=row><md-input-container class=md-block flex=50 flex-xs layout-xs=row layout-sm=row><label>{{\'label-tipo\' | translate}}:*</label><md-select ng-disabled=detalharLaudoRelatorio ng-model=variavel.tipoVariavel name=\"{{nomeCampo}} - {{\'label-tipo\' | translate}}\" placeholder=\"{{\'label-tipo\' | translate}}:*\"><md-option ng-repeat=\"(k,v) in listaTiposVariavel\" ng-value=k>{{v}}</md-option></md-select></md-input-container><md-radio-group ng-disabled=detalharLaudoRelatorio flex=20 class=tamanho-radio-button-avaliacao ng-model=variavel.isVariavelUtilizadaModelo name=\"{{nomeCampo}} - {{\'label-variavel-utilizada-modelo\' | translate}}\"><div layout=row><md-radio-button ng-repeat=\"(k,v) in listaCondicoes\" layout-align=space-between class=\"tamanho-radio-button-avaliacao md-primary\" flex=50 ng-value=v><div class=texto-preto>{{k == \'SIM\' ? \'Sim\' : \'Não\'}}</div></md-radio-button></div></md-radio-group></div><div layout=row><md-input-container layout-xs=row flex=100 flex-xs flex-sm class=md-block><label>{{\'title-descricao\' | translate}}:*</label><textarea ng-disabled=detalharLaudoRelatorio class=barraRolagemTextArea ng-model=variavel.descricao ui-mask-placeholder-char=space name=\"{{nomeCampo}} - {{\'title-descricao\' | translate}}\"></textarea></md-input-container></div><div class=m-t-20 flex layout=row layout-align=end-end><md-button ng-show=\"!detalharLaudoRelatorio && !editar.buttonShow\" class=\"md-primary md-raised\" id=buttonAdicionarVariavel ng-click=adicionarVariavel() style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-adicionar\' | translate}}</label></md-button><md-button ng-show=\"!detalharLaudoRelatorio && editar.buttonShow\" class=\"md-primary md-raised\" id=buttonEditarVariavel ng-click=gravarAlteracaoVariavel() style=\"margin: 0 !important;font-weight:normal; text-transform:none\"><label>{{\'button-editar\' | translate}}</label></md-button></div><div flex class=caixa-tabela-resultado ng-show=\"paginationVariaveis.length > 0\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-nome\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-variavel\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-tipo\' | translate}}</th><th style=\"width: 20%\" class=th-margin-pvgs md-column>{{\'label-descricao\' | translate}}</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'label-utilizada\' | translate}}</th><th style=\"width: 5%\" ng-show=!detalharLaudoRelatorio class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"variavel in paginationVariaveis\"><td md-cell class=th-margin-pvgs>{{variavel.nome == \'OUTRO\' ? variavel.nomeOutraVariavel : listaNomesVariavelCompleta[variavel.nome]}}</td><td md-cell class=th-margin-pvgs ng-bind=variavel.variavel></td><td md-cell class=th-margin-pvgs ng-bind=variavel.tipoVariavel></td><td md-cell class=th-margin-pvgs><a href=\"\" class=descricao-tabela ng-click=\"showDialogDescricaoVariavel($event, variavel)\" target=_blank>{{variavel.descricao}}</a></td><td md-cell class=th-margin-pvgs>{{variavel.isVariavelUtilizadaModelo ? \'Sim\' : \'Não\'}}</td><td md-cell ng-show=!detalharLaudoRelatorio><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=alterarVariavel($index) ng-show=!detalharLaudoRelatorio class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-editar\' | translate}}\" ng-bind=\"\'edit\'\" aria-label=\"{{\'label-editar\' | translate}}\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=\"removerVariavel($index, variavel.id)\" ng-show=!detalharLaudoRelatorio class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir\' | translate}}\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoVariaveis md-page-select></md-table-pagination></md-content></div></div></fieldset><div class=md-block flex-gt-xs flex=50 ng-show=!detalharLaudoRelatorio><md-button class=\"md-primary md-raised upload-button\" type=button ng-click=gerarTabelaVariaveis(paginationVariaveis) ng-show=\"avaliacao.variaveis && avaliacao.variaveis.length > 0\"><label>Gerar tabela de variáveis para download</label></md-button></div><fieldset class=fieldsetArredondado layout=column layout-xs=column ng-show=\"paginationVariaveis.length > 0\"><div layout=row flex layout-align=\"start start\" class=m-t-20><h3 class=subtitulo-pagina>{{\'label-amostra\' | translate}}</h3></div><div flex layout=row layout-align=\"start start\"><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" ng-click=incluirAmostra() style=\"font-weight:normal; text-transform:none\"><label>{{\'button-incluir-dado\' | translate}}</label></md-button><md-button ngf-multiple=false ngf-select=importarAmostra($files) ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised m-l-10\" style=\"font-weight:normal; text-transform:none\"><label>{{\'button-importar-dado\' | translate}}</label></md-button><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised m-l-10\" ng-click=selecionarAmostra() style=\"font-weight:normal; text-transform:none\"><label>{{\'button-utilizar-dado-cadastrado\' | translate}}</label></md-button></div><div flex class=caixa-tabela-resultado ng-show=\"paginationAmostras.length > 0\"><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th style=\"width: 2%\" class=th-margin-pvgs md-column></th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'label-dado\' | translate}}</th><th ng-repeat=\"variavel in paginationVariaveis\" class=th-margin-pvgs md-column>{{\'label-variavel\' | translate}} {{variavel.nome == \'OUTRO\' ? variavel.nomeOutraVariavel : listaNomesVariavelCompleta[variavel.nome]}}</th><th style=\"width: 10%\" class=th-margin-pvgs md-column>{{\'label-utilizada\' | translate}}</th><th style=\"width: 5%\" ng-show=!detalharLaudoRelatorio class=th-margin-pvgs md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"amostra in paginationAmostras\" ng-style=\"getColorLinha(variaveisAmostras, amostra.id) ? colorTableAmostras : {}\"><td md-cell class=th-margin-pvgs><div><md-icon ng-show=\"$index != 0\" ng-click=changeUp(amostra.id) class=mouse-pointer ng-bind=\"\'keyboard_arrow_up\'\"></md-icon></div><div><md-icon ng-show=\"$index < (paginationAmostras.length - 1)\" ng-click=changeDown(amostra.id) class=mouse-pointer ng-bind=\"\'keyboard_arrow_down\'\"></md-icon></div></td><td md-cell class=th-margin-pvgs ng-bind=\"$index + 1\"></td><td ng-repeat=\"variavelAmostra in variaveisAmostras\" md-cell class=th-margin-pvgs ng-if=\"amostra.id == variavelAmostra.amostra.id\" ng-bind=variavelAmostra.valor></td><td md-cell class=th-margin-pvgs><span>{{getAmostraUtilizada(variaveisAmostras, amostra.id)}}</span></td><td md-cell ng-show=!detalharLaudoRelatorio><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon><md-tooltip>{{\'label-acoes\' | translate}}</md-tooltip></md-button></md-fab-trigger><md-fab-actions><md-button ng-click=editarAmostra(amostra.id) ng-show=!detalharLaudoRelatorio class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-editar\' | translate}}\" ng-bind=\"\'edit\'\" aria-label=\"{{\'label-editar\' | translate}}\"><md-tooltip>{{\'label-editar\' | translate}}</md-tooltip></md-icon></md-button><md-button ng-click=\"removerAmostra($index, amostra.id)\" ng-if=!amostra.usuarioLogado ng-show=!detalharLaudoRelatorio class=\"md-fab md-raised md-mini\"><md-icon alt=\"{{\'label-excluir\' | translate}}\" ng-bind=\"\'delete\'\" aria-label=\"{{\'label-excluir\' | translate}}\"><md-tooltip>{{\'label-excluir\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=paginacaoAmostras md-page-select></md-table-pagination></md-content></div><div ng-show=mostrarAviso class=m-t-5 layout-align=\"start start\" flex=100 layout=row><label style=\"color: red; font-size: 12px\">{{\'label-dados-vermelho-necessitam-complemento\' | translate}}</label></div></div></fieldset><fieldset class=fieldsetArredondado layout=column layout-xs=column ng-show=\"(paginationVariaveis | filter: { variavel: \'INDEPENDENTE\' }).length > 0\"><div layout=row flex layout-align=\"start start\" class=m-t-20><h3 class=subtitulo-pagina>{{\'label-atributos-avaliando\' | translate}}</h3></div><div layout=row ng-repeat=\"variavel in paginationVariaveis | filter: { variavel: \'INDEPENDENTE\' }\"><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block ng-if=\"variavel.tipoVariavel == \'TEXTO\'\"><label>{{variavel.nome == \'OUTRO\' ? variavel.nomeOutraVariavel : listaNomesVariavelCompleta[variavel.nome]}}:</label><input type=text ng-model=variavel.valor ng-blur=changeValorVariavel() maxlength=500 ng-maxlength=500> <span style=\"color: #cc0000; float: right\">{{0 + variavel.valor.length}}/500</span></md-input-container><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block ng-if=\"variavel.tipoVariavel != \'TEXTO\'\"><label>{{variavel.nome == \'OUTRO\' ? variavel.nomeOutraVariavel : listaNomesVariavelCompleta[variavel.nome]}}:</label><input type=text ng-model=variavel.valor ng-blur=changeValorVariavel() ui-number-mask=0 maxlength=50 ng-maxlength=50> <span style=\"color: #cc0000; float: right\">{{0 + variavel.valor.toString().length}}/50</span></md-input-container><md-input-container layout-xs=row flex=50 flex-xs flex-sm class=md-block><label>{{\'title-descricao\' | translate}}:</label><input type=text ng-model=variavel.descricaoAtributoAvaliando maxlength=1000 ng-maxlength=1000> <span style=\"color: #cc0000; float: right\">{{0 + variavel.descricaoAtributoAvaliando.length}}/1000</span></md-input-container></div></fieldset><div layout=row layout-xs=column flex-sm=column ng-if=carregarMapa><md-input-container class=md-block flex=100 flex-xs=100 flex-sm=100><ci-mapa-avaliacao showminimapa=true heightminimapa=50 widthminimapa=50 mostrar-amostras=true imovel=imovel bt-marcar=false bt-camada=true bt-remover=false mapa-variaveis-amostras=true latitude=amostras[0].latitude longitude=amostras[0].longitude bloquear-mapa=false bt-selecionar=false></ci-mapa-avaliacao></md-input-container></div>");
$templateCache.put("scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorLaudoBenfeitorias.html","<div flex class=caixa-tabela-resultado><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-id\' | translate}}</th><th md-column>{{\'label-tipo\' | translate}}</th><th md-column>{{\'label-descricao\' | translate}}</th><th md-column>{{\'label-area-construida\' | translate}} {{$parent.unidadeMedida}}</th><th md-column>{{\'label-cub\' | translate}}</th><th md-column>{{\'tr-valor-estimado\' | translate}} {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}}</th></tr></thead><tbody md-body><tr md-row ng-if=\"benfeitoriasImovel.length == 0\"><td md-cell colspan=7>{{\'tr-imovel-nao-possui-benfeitoria\' | translate}}.</td></tr><tr md-row ng-repeat=\"benfeitoria in benfeitoriasImovel\" ng-if=\"benfeitoria.tipoBenfeitoria.descricao != \'Complementar\'\"><td md-cell><label ng-bind=benfeitoria.codigo></label></td><td md-cell><label ng-bind=benfeitoria.tipoBenfeitoria.descricao></label></td><td md-cell><label ng-bind=\"(benfeitoria.edificacao ? benfeitoria.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></td><td md-cell><label ng-bind=\"(benfeitoria.areaConstruida | currency).replace(\'R$\',\'\')\"></label></td><td md-cell><div ng-if=\"!benfeitoria.padraoAcabamento && !benfeitoria.tipoProjetoCub\"><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" ng-click=goInformarValorCub(benfeitoria)><label>{{\'button-informar-cub\' | translate}}</label></md-button><label ng-show=detalharLaudoRelatorio>{{\'-\'}}</label></div><div layout=row ng-if=benfeitoria.padraoAcabamento><a href=\"\" ng-click=\"visualizarEstimativasValor($event, benfeitoria)\"><label ng-show=\"benfeitoria.padraoAcabamento != \'CUB_MEDIO\' && benfeitoria.tipoProjetoCub\" ng-bind=\"listaPadraoAcabamento[benfeitoria.padraoAcabamento] + \'/\' + listaTiposProjeto[benfeitoria.tipoProjetoCub]\"></label><label ng-show=\"benfeitoria.padraoAcabamento == \'CUB_MEDIO\'\" ng-bind=listaPadraoAcabamento[benfeitoria.padraoAcabamento]></label></a><div ng-if=benfeitoria.tipoProjetoCub><a href=\"\" ng-click=goInformarValorCub(benfeitoria) flex=5 ng-show=!detalharLaudoRelatorio><ng-md-icon icon=edit style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Editar\'}}</md-tooltip></ng-md-icon></a></div></div></td><td class=alinhamentoTextoCentro md-cell><label><label ng-bind=\"(benfeitoria.valor | currency).replace(\'R$\',\'\')\"></label></label></td></tr></tbody></table></md-table-container></md-content></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorLaudoRelatorioBenfeitorias.html","<div flex class=caixa-tabela-resultado><div layout=column><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-id\' | translate}}</th><th md-column>{{\'label-tipo\' | translate}}</th><th md-column>{{\'label-descricao\' | translate}}</th><th md-column>{{\'label-area-contruida\' | translate}} {{$parent.unidadeMedida}}</th><th md-column>{{\'label-cub\' | translate}}</th><th md-column>{{\'tr-valor-estimado\' | translate}} {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in benfeitoriasImovel\" ng-if=\"benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao != \'Complementar\'\"><td md-cell><label ng-bind=benfeitoria.benfeitoriaImovel.codigo></label></td><td md-cell><label ng-bind=benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao></label></td><td md-cell><label ng-bind=\"(benfeitoria.benfeitoriaImovel.edificacao ? benfeitoria.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></td><td md-cell><label ng-bind=\"(benfeitoria.benfeitoriaImovel.areaConstruida | currency).replace(\'R$\',\'\')\"></label></td><td md-cell ng-if=\"!benfeitoria.benfeitoriaImovel.padraoAcabamento && !benfeitoria.benfeitoriaImovel.tipoProjetoCub\"><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" ng-click=goInformarValorCub(benfeitoria)><label>{{\'button-informar-cub\' | translate}}</label></md-button></td><td md-cell ng-if=\"benfeitoria.benfeitoriaImovel.padraoAcabamento && benfeitoria.benfeitoriaImovel.tipoProjetoCub\"><div layout=row><a href=\"\" ng-click=\"visualizarEstimativasValor($event, benfeitoria.benfeitoriaImovel)\"><label ng-show=\"benfeitoria.benfeitoriaImovel.padraoAcabamento != \'CUB_MEDIO\'\" ng-bind=\"listaPadraoAcabamento[benfeitoria.benfeitoriaImovel.padraoAcabamento] + \'/\' + listaTiposProjeto[benfeitoria.benfeitoriaImovel.tipoProjetoCub]\"></label><label ng-show=\"benfeitoria.benfeitoriaImovel.padraoAcabamento == \'CUB_MEDIO\'\" ng-bind=listaPadraoAcabamento[benfeitoria.benfeitoriaImovel.padraoAcabamento]></label></a><a href=\"\" ng-click=goInformarValorCub(benfeitoria) flex=5 ng-show=!detalharLaudoRelatorio><ng-md-icon icon=edit style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Editar\'}}</md-tooltip></ng-md-icon></a></div></td><td class=alinhamentoTextoCentro md-cell><label><label ng-bind=\"(benfeitoria.benfeitoriaImovel.valor | currency).replace(\'R$\',\'\')\"></label></label></td></tr></tbody></table></md-table-container></md-conent></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorLaudoUnidadeAutonoma.html","<div flex class=caixa-tabela-resultado><div layout=column><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-id\' | translate}}</th><th md-column>{{\'label-tipo\' | translate}}</th><th md-column>{{\'label-descricao\' | translate}}</th><th md-column>{{\'label-area-contruida\' | translate}} {{$parent.unidadeMedida}}</th><th md-column>{{\'label-cub\' | translate}}</th><th md-column>{{\'tr-valor-estimado\' | translate}} {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label ng-bind=unidadeAutonomaImovel[0].codigo></label></td><td md-cell><label ng-bind=unidadeAutonomaImovel[0].edificacao.tipoEdificacao.descricao></label></td><td md-cell><label ng-bind=unidadeAutonomaImovel[0].edificacao.tipoProjeto.descricao></label></td><td md-cell><label ng-bind=\"(unidadeAutonomaImovel[0].area | currency).replace(\'R$\',\'\')\"></label></td><td md-cell ng-if=\"!unidadeAutonomaImovel[0].padraoAcabamento && !unidadeAutonomaImovel[0].tipoProjetoCub\"><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" ng-click=goInformarValorCub(unidadeAutonomaImovel[0])><label>{{\'button-informar-cub\' | translate}}</label></md-button></td><td md-cell ng-if=\"unidadeAutonomaImovel[0].padraoAcabamento && unidadeAutonomaImovel[0].tipoProjetoCub\"><div layout=row><a href=\"\" ng-click=\"visualizarEstimativasValor($event, unidadeAutonomaImovel[0])\"><label ng-bind=\"listaPadraoAcabamento[unidadeAutonomaImovel[0].padraoAcabamento] + \'/\' + listaTiposProjeto[unidadeAutonomaImovel[0].tipoProjetoCub]\"></label></a><a href=\"\" ng-click=goInformarValorCub() flex=5 ng-show=!detalharLaudoRelatorio><ng-md-icon icon=edit style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Editar\'}}</md-tooltip></ng-md-icon></a></div></td><td class=alinhamentoTextoCentro md-cell><label><label ng-bind=\"(unidadeAutonomaImovel[0].valor | currency).replace(\'R$\',\'\')\"></label></label></td></tr></tbody></table></md-table-container></md-conent></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorTerrenoLaudoBenfeitorias.html","<div flex class=caixa-tabela-resultado><div layout=column><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-id\' | translate}}</th><th md-column>{{\'label-tipo\' | translate}}</th><th md-column>{{\'label-descricao\' | translate}}</th><th md-column>{{\'label-area-contruida\' | translate}} {{$parent.unidadeMedida}}</th><th md-column>{{\'label-cub\' | translate}}</th><th md-column>{{\'label-valor\' | translate}} {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}}</th></tr></thead><tbody md-body><tr md-row ng-if=\"benfeitoriasImovel.length == 0\"><td md-cell colspan=7>{{\'tr-imovel-nao-possui-benfeitoria\' | translate}}.</td></tr><tr md-row ng-repeat=\"benfeitoria in benfeitoriasImovel\" ng-if=\"benfeitoria.tipoBenfeitoria.descricao != \'Complementar\'\"><td md-cell><label ng-bind=benfeitoria.codigo></label></td><td md-cell><label ng-bind=benfeitoria.tipoBenfeitoria.descricao></label></td><td md-cell><label ng-bind=\"(benfeitoria.edificacao ? benfeitoria.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></td><td md-cell><label ng-bind=\"(benfeitoria.areaConstruida | currency).replace(\'R$\',\'\')\"></label></td><td md-cell ng-if=\"!benfeitoria.padraoAcabamento && !benfeitoria.tipoProjetoCub\"><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" ng-click=goInformarValorCub(benfeitoria)><label>{{\'button-informar-cub\' | translate}}</label></md-button></td><td md-cell ng-if=\"benfeitoria.padraoAcabamento && benfeitoria.tipoProjetoCub\"><div layout=row><a href=\"\" ng-click=\"visualizarEstimativasValor($event, benfeitoria)\"><label ng-show=\"benfeitoria.padraoAcabamento != \'CUB_MEDIO\'\" ng-bind=\"listaPadraoAcabamento[benfeitoria.padraoAcabamento] + \'/\' + listaTiposProjeto[benfeitoria.tipoProjetoCub]\"></label><label ng-show=\"benfeitoria.padraoAcabamento == \'CUB_MEDIO\'\" ng-bind=listaPadraoAcabamento[benfeitoria.padraoAcabamento]></label></a><a href=\"\" ng-click=goInformarValorCub(benfeitoria) flex=5 ng-show=!detalharLaudoRelatorio><ng-md-icon icon=edit style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Editar\'}}</md-tooltip></ng-md-icon></a></div></td><td class=alinhamentoTextoCentro md-cell><label><label ng-bind=\"(benfeitoria.valor | currency).replace(\'R$\',\'\')\"></label></label></td></tr></tbody></table></md-table-container></md-conent></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorTerrenoLaudoRelatorioBenfeitorias.html","<div flex class=caixa-tabela-resultado><div layout=column><md-conent flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-id\' | translate}}</th><th md-column>{{\'label-tipo\' | translate}}</th><th md-column>{{\'label-descricao\' | translate}}</th><th md-column>{{\'label-area-contruida\' | translate}} {{$parent.unidadeMedida}}</th><th md-column>{{\'label-cub\' | translate}}</th><th md-column>{{\'label-valor\' | translate}} {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in benfeitoriasImovel\" ng-if=\"benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao != \'Complementar\'\"><td md-cell><label ng-bind=benfeitoria.benfeitoriaImovel.codigo></label></td><td md-cell><label ng-bind=benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao></label></td><td md-cell><label ng-bind=\"(benfeitoria.benfeitoriaImovel.edificacao ? benfeitoria.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></td><td md-cell><label ng-bind=\"(benfeitoria.benfeitoriaImovel.areaConstruida | currency).replace(\'R$\',\'\')\"></label></td><td md-cell ng-if=\"!benfeitoria.benfeitoriaImovel.padraoAcabamento && !benfeitoria.benfeitoriaImovel.tipoProjetoCub\"><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" ng-click=goInformarValorCub(benfeitoria)><label>{{\'button-informar-cub\' | translate}}</label></md-button></td><td md-cell ng-if=\"benfeitoria.benfeitoriaImovel.padraoAcabamento && benfeitoria.benfeitoriaImovel.tipoProjetoCub\"><div layout=row><a href=\"\" ng-click=\"visualizarEstimativasValor($event, benfeitoria)\"><label ng-show=\"benfeitoria.benfeitoriaImovel.padraoAcabamento != \'CUB_MEDIO\'\" ng-bind=\"listaPadraoAcabamento[benfeitoria.benfeitoriaImovel.padraoAcabamento] + \'/\' + listaTiposProjeto[benfeitoria.benfeitoriaImovel.tipoProjetoCub]\"></label><label ng-show=\"benfeitoria.benfeitoriaImovel.padraoAcabamento == \'CUB_MEDIO\'\" ng-bind=listaPadraoAcabamento[benfeitoria.benfeitoriaImovel.padraoAcabamento]></label></a><a href=\"\" ng-click=goInformarValorCub(benfeitoria) flex=5 ng-show=!detalharLaudoRelatorio><ng-md-icon icon=edit style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Editar\'}}</md-tooltip></ng-md-icon></a></div></td><td class=alinhamentoTextoCentro md-cell><label><label ng-bind=\"(benfeitoria.benfeitoriaImovel.valor | currency).replace(\'R$\',\'\')\"></label></label></td></tr></tbody></table></md-table-container></md-conent></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/tabelas/templates/tableEstimarValorTerrenoLaudoUnidadeAutonoma.html","<div flex class=caixa-tabela-resultado><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-id\' | translate}}</th><th md-column>{{\'label-tipo\' | translate}}</th><th md-column>{{\'label-descricao\' | translate}}</th><th md-column>{{\'label-area-construida\' | translate}} {{$parent.unidadeMedida}}</th><th md-column>{{\'label-cub\' | translate}}</th><th md-column>{{\'label-valor\' | translate}} {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label ng-bind=unidadeAutonomaImovel[0].codigo></label></td><td md-cell><label ng-bind=unidadeAutonomaImovel[0].edificacao.tipoEdificacao.descricao></label></td><td md-cell><label ng-bind=unidadeAutonomaImovel[0].edificacao.tipoProjeto.descricao></label></td><td md-cell><label ng-bind=\"(unidadeAutonomaImovel[0].area | currency).replace(\'R$\',\'\')\"></label></td><td md-cell ng-if=\"!unidadeAutonomaImovel[0].padraoAcabamento && !unidadeAutonomaImovel[0].tipoProjetoCub\"><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" ng-click=goInformarValorCub(benfeitoria)><label>{{\'button-informar-cub\' | translate}}</label></md-button></td><td md-cell ng-if=\"unidadeAutonomaImovel[0].padraoAcabamento && unidadeAutonomaImovel[0].tipoProjetoCub\"><div layout=row><label ng-bind=\"listaPadraoAcabamento[unidadeAutonomaImovel[0].padraoAcabamento] + \'/\' + listaTiposProjeto[unidadeAutonomaImovel[0].tipoProjetoCub]\"></label><a href=\"\" ng-click=goInformarValorCub() flex=5 ng-show=!detalharLaudoRelatorio><ng-md-icon icon=edit style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Habilitar\'}}</md-tooltip></ng-md-icon></a></div></td><td md-cell><md-input-container class=md-block style=margin-bottom:0px flex=50 flex-sx=100><input type=value ng-model=unidadeAutonomaImovel[0].valor ui-number-mask=2 ng-blur=calcularValorTerreno(); name=\"{{nomeCampo}} - Valor\" required ng-disabled=detalharLaudoRelatorio></md-input-container></td></tr></tbody></table></md-table-container></md-content></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/tabelas/templates/tableInformarValorEdificacao.html","<div flex class=caixa-tabela-resultado><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-id\' | translate}}</th><th md-column>{{\'label-tipo\' | translate}}</th><th md-column>{{\'label-descricao\' | translate}}</th><th md-column>{{\'label-area-construida\' | translate}} {{$parent.unidadeMedida}}</th><th md-column>{{\'label-valor\' | translate}} {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}}</th><th class=th-margin-pvgs md-column>{{\'tr-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"benfeitoria in benfeitoriasImovel\"><td md-cell><label ng-bind=benfeitoria.benfeitoriaImovel.codigo></label></td><td md-cell><label ng-bind=benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao></label></td><td md-cell><label ng-bind=\"(benfeitoria.benfeitoriaImovel.edificacao ? benfeitoria.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></td><td md-cell><label ng-bind=\"(benfeitoria.benfeitoriaImovel.areaConstruida | currency).replace(\'R$\',\'\')\"></label></td><td md-cell><label ng-bind=\"(benfeitoria.benfeitoriaImovel.valorEdificacao.valor | currency).replace(\'R$\',\'\')\"></label></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=adicionarValor(benfeitoria) ng-show=\"benfeitoria.benfeitoriaImovel.valorEdificacao.valor == null\"><md-icon ng-bind=\"\'add\'\"><md-tooltip>{{\'label-adicionar-valor\' | translate}}</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=adicionarValor(benfeitoria); ng-show=\"benfeitoria.benfeitoriaImovel.valorEdificacao.valor != null\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>{{\'label-editar-valor\' | translate}}</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container></md-content></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/tabelas/templates/tableInformarValorLaudoBenfeitorias.html","<div flex class=caixa-tabela-resultado><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-id\' | translate}}</th><th md-column>{{\'label-tipo\' | translate}}</th><th md-column>{{\'label-descricao\' | translate}}</th><th md-column>{{\'label-area-construida\' | translate}} {{$parent.unidadeMedida}}</th><th md-column>{{\'label-cub-depreciado\' | translate}}<ng-md-icon icon icon=help class=cub-tooltip-icon size=18><md-tooltip md-direction=top>Deve ser informado o valor já depreciado do CUB.</md-tooltip></ng-md-icon></th><th md-column ng-if=\"tipoValorLaudo == \'ESTIMAR_VALORES\'\">{{ \'tr-valor-estimado\' | translate }} da Benfeitoria {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}} *</th><th md-column ng-if=\"tipoValorLaudo == \'INFORMAR_VALORES_LAUDO\'\">{{ \'label-valor\' | translate }} da Benfeitoria {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}} *</th></tr></thead><tbody md-body><tr md-row ng-if=\"benfeitoriasImovel.length == 0\"><td md-cell colspan=7>{{\'tr-imovel-nao-possui-benfeitoria\' | translate}}.</td></tr><tr md-row ng-repeat=\"benfeitoria in benfeitoriasImovel\"><td md-cell><label ng-bind=benfeitoria.codigo></label></td><td md-cell><label ng-bind=benfeitoria.tipoBenfeitoria.descricao></label></td><td md-cell><label ng-bind=\"(benfeitoria.edificacao ? benfeitoria.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></td><td md-cell><label ng-bind=\"(benfeitoria.areaConstruida | currency).replace(\'R$\',\'\')\"></label></td><!-- coluna de CUB desabilitada, pois de acordo com o IB 4734672, será implementado na Fase 2 do projeto --><!--<td md-cell>\n                            <div ng-if=\"!benfeitoria.padraoAcabamento && !benfeitoria.tipoProjetoCub\">\n                                <md-button ng-show=\"!detalharLaudoRelatorio\" class=\"md-primary md-raised\" ng-click=\"goInformarValorCub(benfeitoria)\">\n                                    <label>{{\'button-informar-cub\' | translate}}</label>\n                                </md-button>\n                                <label ng-show=\"detalharLaudoRelatorio\">{{\'-\'}}</label>\n                            </div>\n                            <div layout=\"row\" ng-if=\"benfeitoria.padraoAcabamento\">\n                                <a href=\"\" ng-click=\"visualizarEstimativasValor($event, benfeitoria)\">\n                                    <label ng-show=\"benfeitoria.padraoAcabamento != \'CUB_MEDIO\' && benfeitoria.tipoProjetoCub\" ng-bind=\"listaPadraoAcabamento[benfeitoria.padraoAcabamento] + \'/\' + listaTiposProjeto[benfeitoria.tipoProjetoCub]\"></label>\n                                    <label ng-show=\"benfeitoria.padraoAcabamento == \'CUB_MEDIO\' || (benfeitoria.padraoAcabamento != \'CUB_MEDIO\' && !benfeitoria.tipoProjetoCub)\" ng-bind=\"listaPadraoAcabamento[benfeitoria.padraoAcabamento]\"></label>\n                                </a>\n                                <div ng-if=\"benfeitoria.tipoProjetoCub\">\n                                    <a href=\"\" ng-click=\"goInformarValorCub(benfeitoria)\" flex=\"5\" ng-show=\"!detalharLaudoRelatorio\">\n                                        <ng-md-icon icon=\"edit\" style=\"fill:#788489\" size=\"22\" alt=\"Visualizar Avaliação\">\n                                            <md-tooltip ng-if=\"tipoValorLaudo == \'ESTIMAR_VALORES\'\">{{\'Editar\'}}</md-tooltip>\n                                            <md-tooltip ng-if=\"tipoValorLaudo == \'INFORMAR_VALORES_LAUDO\'\">{{\'Habilitar\'}}</md-tooltip>\n                                        </ng-md-icon>\n                                    </a>\n                                </div>\n                            </div>\n                        </td>--><td class=alinhamentoTextoCentro md-cell><div ng-if=\"tipoValorLaudo == \'INFORMAR_VALORES_LAUDO\'\"><md-input-container class=\"md-block input-container-valor-benfeitoria\"><div class=div-input-centralizada><input type=value ng-model=benfeitoria.valorCub ui-number-mask=2 ng-change=limparValorBenfeitoria(benfeitoria) ng-blur=calcularCubDepreciado(benfeitoria) ng-disabled=detalharLaudoRelatorio aria-label=\"CUB Depreciado\"></div></md-input-container></div><div ng-if=\"tipoValorLaudo == \'ESTIMAR_VALORES\'\"><label><label ng-bind=\"(benfeitoria.valorCub | currency).replace(\'R$\',\'\')\"></label></label></div></td><td class=alinhamentoTextoCentro md-cell><div ng-if=\"tipoValorLaudo == \'INFORMAR_VALORES_LAUDO\'\"><md-input-container class=\"md-block input-container-valor-benfeitoria\"><div class=div-input-centralizada><input type=value ng-model=benfeitoria.valor ui-number-mask=2 ng-change=limparCub(benfeitoria) ng-blur=calcularValorTerreno(benfeitoria); name=\"{{nomeCampo}} - Valor\" required ng-disabled=detalharLaudoRelatorio aria-label=\"Valor Benfeitoria\" ng-class=md-input-invalid></div></md-input-container></div><div ng-if=\"tipoValorLaudo == \'ESTIMAR_VALORES\'\"><label><label ng-bind=\"(benfeitoria.valor | currency).replace(\'R$\',\'\')\"></label></label></div></td></tr></tbody></table></md-table-container></md-content></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/tabelas/templates/tableInformarValorLaudoRelatorioBenfeitorias.html","<div flex class=caixa-tabela-resultado><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-id\' | translate}}</th><th md-column>{{\'label-tipo\' | translate}}</th><th md-column>{{\'label-descricao\' | translate}}</th><th md-column>{{\'label-area-construida\' | translate}} {{$parent.unidadeMedida}}</th><th md-column>{{\'label-cub\' | translate}}</th><th md-column>{{\'label-valor\' | translate}} da Benfeitoria {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}}</th></tr></thead><tbody md-body><tr md-row ng-if=\"benfeitoriasImovel.length == 0\"><td md-cell colspan=7>{{\'tr-imovel-nao-possui-benfeitoria\' | translate}}.</td></tr><tr md-row ng-repeat=\"benfeitoria in benfeitoriasImovel\"><td md-cell><label ng-bind=benfeitoria.benfeitoriaImovel.codigo></label></td><td md-cell><label ng-bind=benfeitoria.benfeitoriaImovel.tipoBenfeitoria.descricao></label></td><td md-cell><label ng-bind=\"(benfeitoria.benfeitoriaImovel.edificacao ? benfeitoria.benfeitoriaImovel.edificacao.tipoEspecializacaoEdificacao.descricao : benfeitoria.benfeitoriaImovel.benfeitoriaComplementar.tipoEspecializacaoComplementar.descricao)\"></label></td><td md-cell><label ng-bind=\"(benfeitoria.benfeitoriaImovel.areaConstruida | currency).replace(\'R$\',\'\')\"></label></td><td md-cell ng-if=\"!benfeitoria.benfeitoriaImovel.padraoAcabamento && !benfeitoria.benfeitoriaImovel.tipoProjetoCub\"><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" type=button ng-click=goInformarValorCub(benfeitoria)><label>{{\'button-informar-cub\' | translate}}</label></md-button></td><td md-cell ng-if=\"benfeitoria.benfeitoriaImovel.padraoAcabamento && benfeitoria.benfeitoriaImovel.tipoProjetoCub\"><div layout=row><a href=\"\" ng-click=\"visualizarEstimativasValor($event, benfeitoria.benfeitoriaImovel)\"><label ng-show=\"benfeitoria.benfeitoriaImovel.padraoAcabamento != \'CUB_MEDIO\'\" ng-bind=\"listaPadraoAcabamento[benfeitoria.benfeitoriaImovel.padraoAcabamento] + \'/\' + listaTiposProjeto[benfeitoria.benfeitoriaImovel.tipoProjetoCub]\"></label><label ng-show=\"benfeitoria.benfeitoriaImovel.padraoAcabamento == \'CUB_MEDIO\'\" ng-bind=listaPadraoAcabamento[benfeitoria.benfeitoriaImovel.padraoAcabamento]></label></a><a href=\"\" ng-click=goInformarValorCub(benfeitoria) flex=5 ng-show=!detalharLaudoRelatorio><ng-md-icon icon=edit style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Habilitar\'}}</md-tooltip></ng-md-icon></a></div></td><td md-cell><md-input-container class=md-block style=margin-bottom:0px flex=50 flex-sx=100><input type=value ng-model=benfeitoria.benfeitoriaImovel.valor ui-number-mask=2 ng-blur=calcularValorTerreno() name=\"{{nomeCampo}} - Valor\" required ng-disabled=detalharLaudoRelatorio></md-input-container></td></tr></tbody></table></md-table-container></md-content></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/tabelas/templates/tableInformarValorLaudoUnidadeAutonoma.html","<div flex class=caixa-tabela-resultado><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-id\' | translate}}</th><th md-column>{{\'label-tipo\' | translate}}</th><th md-column>{{\'label-descricao\' | translate}}</th><th md-column>{{\'label-area-construida\' | translate}} {{$parent.unidadeMedida}}</th><th md-column>{{\'label-cub\' | translate}}</th><th md-column>{{\'label-valor\' | translate}} {{$parent.moeda ? $parent.moeda.simbolo : $parent.listaMoedas[0].simbolo}}</th></tr></thead><tbody md-body><tr md-row><td md-cell><label ng-bind=unidadeAutonomaImovel[0].codigo></label></td><td md-cell><label ng-bind=unidadeAutonomaImovel[0].edificacao.tipoEdificacao.descricao></label></td><td md-cell><label ng-bind=unidadeAutonomaImovel[0].edificacao.tipoProjeto.descricao></label></td><td md-cell><label ng-bind=\"(unidadeAutonomaImovel[0].area | currency).replace(\'R$\',\'\')\"></label></td><td md-cell ng-if=\"!unidadeAutonomaImovel[0].padraoAcabamento && !unidadeAutonomaImovel[0].tipoProjetoCub\"><md-button ng-show=!detalharLaudoRelatorio class=\"md-primary md-raised\" ng-click=goInformarValorCub(benfeitoria)><label>{{\'button-informar-cub\' | translate}}</label></md-button></td><td md-cell ng-if=\"unidadeAutonomaImovel[0].padraoAcabamento && unidadeAutonomaImovel[0].tipoProjetoCub\"><div layout=row><label ng-bind=\"listaPadraoAcabamento[unidadeAutonomaImovel[0].padraoAcabamento] + \'/\' + listaTiposProjeto[unidadeAutonomaImovel[0].tipoProjetoCub]\"></label><a href=\"\" ng-click=goInformarValorCub() flex=5 ng-show=!detalharLaudoRelatorio><ng-md-icon icon=edit style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Habilitar\'}}</md-tooltip></ng-md-icon></a></div></td><td md-cell><md-input-container class=md-block style=margin-bottom:0px flex=50 flex-sx=100><input type=value ng-model=unidadeAutonomaImovel[0].valor ui-number-mask=2 ng-blur=calcularValorTerreno(); name=\"{{nomeCampo}} - Valor\" required ng-disabled=detalharLaudoRelatorio></md-input-container></td></tr></tbody></table></md-table-container></md-content></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/utilitarios/templates/contadorRomano.tmpl.html","{{number.value}}");
$templateCache.put("scripts-avaliacao/app/directives/utilitarios/templates/habilitarEdicaoCampo.tmpl.html","<div layout=row class=alinharButtonsEdicao><a href=\"\" ng-click=habilitarEdicaoCampo(); flex=5 style=\"padding-left: 10px;cursor: pointer\"><ng-md-icon icon=edit style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Habilitar\'}}</md-tooltip></ng-md-icon></a></div>");
$templateCache.put("scripts-avaliacao/app/directives/utilitarios/templates/manterEdicaoCampo.tmpl.html","<div layout=row class=alinharButtonsEdicao><a href=\"\" ng-click=\"isCampo = salvarEdicaoCampo();\" flex=5 style=\"padding-left: 10px;cursor: pointer\"><ng-md-icon icon=done style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Salvar\'}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=\"isCampo = cancelarEdicaoCampo();\" flex=5 flex-offset=0 style=\"padding-left: 40px;cursor: pointer\"><ng-md-icon icon=clear style=fill:#788489 size=22 alt=\"Visualizar Avaliação\"><md-tooltip>{{\'Cancelar\'}}</md-tooltip></ng-md-icon></a></div>");
$templateCache.put("scripts-avaliacao/app/directives/utilitarios/templates/mapaDetalharImovel.html","<div class=mapaImovel><!-- openlayers ol-center=\"posicao\" ol-view=\"view\" ol-markers=\"posicao.markers\" ol-defaults=\"defaults\" width=\"100%\" height=\"100%\">\n        <ol-control name=\"{{ control.name }}\" ng-repeat=\"control in controls\"></ol-control>\n        <ol-marker ng-repeat=\"marker in markers\" lat=\"marker.lat\" lon=\"marker.lon\"></ol-marker>\n    </openlayers --><div ng-if=erroMapa class=erro-mapa style=\"color:red; padding: 20px; text-align:center\"><p>Erro ao carregar o mapa: dados de geometria do imóvel inválidos ou incompletos.</p></div><div ng-show=!erroMapa id=map class=map><div id=popup></div></div></div>");
$templateCache.put("scripts-avaliacao/app/directives/utilitarios/templates/mapaDetalharImovelAvaliacao.html","<div class=mapaImovel><div id=map style=\"height: 300px\"><div id=popup></div></div></div>");}]);
!function(t,e){"object"==typeof exports&&"undefined"!=typeof module?e(exports):"function"==typeof define&&define.amd?define(["exports"],e):e(t.turf={})}(this,function(t){"use strict";function e(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.bbox,o=n.id;if(void 0===t)throw new Error("geometry is required");if(e&&e.constructor!==Object)throw new Error("properties must be an Object");r&&S(r),o&&I(o);var i={type:"Feature"};return o&&(i.id=o),r&&(i.bbox=r),i.properties=e||{},i.geometry=t,i}function n(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var o=n.bbox;if(!t)throw new Error("type is required");if(!e)throw new Error("coordinates is required");if(!Array.isArray(e))throw new Error("coordinates must be an Array");o&&S(o);var a;switch(t){case"Point":a=r(e).geometry;break;case"LineString":a=s(e).geometry;break;case"Polygon":a=i(e).geometry;break;case"MultiPoint":a=d(e).geometry;break;case"MultiLineString":a=l(e).geometry;break;case"MultiPolygon":a=p(e).geometry;break;default:throw new Error(t+" is invalid")}return o&&(a.bbox=o),a}function r(t,n,r){if(!t)throw new Error("coordinates is required");if(!Array.isArray(t))throw new Error("coordinates must be an Array");if(t.length<2)throw new Error("coordinates must be at least 2 numbers long");if(!E(t[0])||!E(t[1]))throw new Error("coordinates must contain numbers");return e({type:"Point",coordinates:t},n,r)}function o(t,e,n){if(!t)throw new Error("coordinates is required");if(!Array.isArray(t))throw new Error("coordinates must be an Array");return u(t.map(function(t){return r(t,e)}),n)}function i(t,n,r){if(!t)throw new Error("coordinates is required");for(var o=0;o<t.length;o++){var i=t[o];if(i.length<4)throw new Error("Each LinearRing of a Polygon must have 4 or more Positions.");for(var a=0;a<i[i.length-1].length;a++){if(0===o&&0===a&&!E(i[0][0])||!E(i[0][1]))throw new Error("coordinates must contain numbers");if(i[i.length-1][a]!==i[0][a])throw new Error("First and last Position are not equivalent.")}}return e({type:"Polygon",coordinates:t},n,r)}function a(t,e,n){if(!t)throw new Error("coordinates is required");if(!Array.isArray(t))throw new Error("coordinates must be an Array");return u(t.map(function(t){return i(t,e)}),n)}function s(t,n,r){if(!t)throw new Error("coordinates is required");if(t.length<2)throw new Error("coordinates must be an array of two or more positions");if(!E(t[0][1])||!E(t[0][1]))throw new Error("coordinates must contain numbers");return e({type:"LineString",coordinates:t},n,r)}function c(t,e,n){if(!t)throw new Error("coordinates is required");if(!Array.isArray(t))throw new Error("coordinates must be an Array");return u(t.map(function(t){return s(t,e)}),n)}function u(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.bbox,r=e.id;if(!t)throw new Error("No features passed");if(!Array.isArray(t))throw new Error("features must be an Array");n&&S(n),r&&I(r);var o={type:"FeatureCollection"};return r&&(o.id=r),n&&(o.bbox=n),o.features=t,o}function l(t,n,r){if(!t)throw new Error("coordinates is required");return e({type:"MultiLineString",coordinates:t},n,r)}function d(t,n,r){if(!t)throw new Error("coordinates is required");return e({type:"MultiPoint",coordinates:t},n,r)}function p(t,n,r){if(!t)throw new Error("coordinates is required");return e({type:"MultiPolygon",coordinates:t},n,r)}function f(t,n,r){if(!t)throw new Error("geometries is required");if(!Array.isArray(t))throw new Error("geometries must be an Array");return e({type:"GeometryCollection",geometries:t},n,r)}function h(t,e){if(void 0===t||null===t||isNaN(t))throw new Error("num is required");if(e&&!(e>=0))throw new Error("precision must be a positive number");var n=Math.pow(10,e||0);return Math.round(t*n)/n}function g(t,e){if(void 0===t||null===t)throw new Error("radians is required");if(e&&"string"!=typeof e)throw new Error("units must be a string");var n=Bu[e||"kilometers"];if(!n)throw new Error(e+" units is invalid");return t*n}function m(t,e){if(void 0===t||null===t)throw new Error("distance is required");if(e&&"string"!=typeof e)throw new Error("units must be a string");var n=Bu[e||"kilometers"];if(!n)throw new Error(e+" units is invalid");return t/n}function y(t,e){return _(m(t,e))}function v(t){if(null===t||void 0===t)throw new Error("bearing is required");var e=t%360;return e<0&&(e+=360),e}function _(t){if(null===t||void 0===t)throw new Error("radians is required");return t%(2*Math.PI)*180/Math.PI}function b(t){if(null===t||void 0===t)throw new Error("degrees is required");return t%360*Math.PI/180}function w(t,e,n){if(null===t||void 0===t)throw new Error("length is required");if(!(t>=0))throw new Error("length must be a positive number");return g(m(t,e),n||"kilometers")}function x(t,e,n){if(null===t||void 0===t)throw new Error("area is required");if(!(t>=0))throw new Error("area must be a positive number");var r=zu[e||"meters"];if(!r)throw new Error("invalid original units");var o=zu[n||"kilometers"];if(!o)throw new Error("invalid final units");return t/r*o}function E(t){return!isNaN(t)&&null!==t&&!Array.isArray(t)}function C(t){return Boolean(t)&&t.constructor===Object}function S(t){if(!t)throw new Error("bbox is required");if(!Array.isArray(t))throw new Error("bbox must be an Array");if(4!==t.length&&6!==t.length)throw new Error("bbox must be an Array of 4 or 6 numbers");t.forEach(function(t){if(!E(t))throw new Error("bbox must only contain numbers")})}function I(t){if(!t)throw new Error("id is required");if(-1===["string","number"].indexOf(typeof t))throw new Error("id must be a number or a string")}function P(){throw new Error("method has been renamed to `radiansToDegrees`")}function A(){throw new Error("method has been renamed to `degreesToRadians`")}function N(){throw new Error("method has been renamed to `lengthToDegrees`")}function M(){throw new Error("method has been renamed to `lengthToRadians`")}function L(){throw new Error("method has been renamed to `radiansToLength`")}function D(){throw new Error("method has been renamed to `bearingToAzimuth`")}function O(){throw new Error("method has been renamed to `convertLength`")}function T(t,e,n){if(null!==t)for(var r,o,i,a,s,c,u,l,d=0,p=0,f=t.type,h="FeatureCollection"===f,g="Feature"===f,m=h?t.features.length:1,y=0;y<m;y++){u=h?t.features[y].geometry:g?t.geometry:t,l=!!u&&"GeometryCollection"===u.type,s=l?u.geometries.length:1;for(var v=0;v<s;v++){var _=0,b=0;if(null!==(a=l?u.geometries[v]:u)){c=a.coordinates;var w=a.type;switch(d=!n||"Polygon"!==w&&"MultiPolygon"!==w?0:1,w){case null:break;case"Point":e(c,p,y,_,b),p++,_++;break;case"LineString":case"MultiPoint":for(r=0;r<c.length;r++)e(c[r],p,y,_,b),p++,"MultiPoint"===w&&_++;"LineString"===w&&_++;break;case"Polygon":case"MultiLineString":for(r=0;r<c.length;r++){for(o=0;o<c[r].length-d;o++)e(c[r][o],p,y,_,b),p++;"MultiLineString"===w&&_++,"Polygon"===w&&b++}"Polygon"===w&&_++;break;case"MultiPolygon":for(r=0;r<c.length;r++){for("MultiPolygon"===w&&(b=0),o=0;o<c[r].length;o++){for(i=0;i<c[r][o].length-d;i++)e(c[r][o][i],p,y,_,b),p++;b++}_++}break;case"GeometryCollection":for(r=0;r<a.geometries.length;r++)T(a.geometries[r],e,n);break;default:throw new Error("Unknown Geometry Type")}}}}}function R(t,e,n,r){var o=n;return T(t,function(t,r,i,a,s){o=0===r&&void 0===n?t:e(o,t,r,i,a,s)},r),o}function F(t,e){var n;switch(t.type){case"FeatureCollection":for(n=0;n<t.features.length;n++)e(t.features[n].properties,n);break;case"Feature":e(t.properties,0)}}function G(t,e,n){var r=n;return F(t,function(t,o){r=0===o&&void 0===n?t:e(r,t,o)}),r}function q(t,e){if("Feature"===t.type)e(t,0);else if("FeatureCollection"===t.type)for(var n=0;n<t.features.length;n++)e(t.features[n],n)}function k(t,e,n){var r=n;return q(t,function(t,o){r=0===o&&void 0===n?t:e(r,t,o)}),r}function U(t){var e=[];return T(t,function(t){e.push(t)}),e}function B(t,e){var n,r,o,i,a,s,c,u,l,d,p=0,f="FeatureCollection"===t.type,h="Feature"===t.type,g=f?t.features.length:1;for(n=0;n<g;n++){for(s=f?t.features[n].geometry:h?t.geometry:t,u=f?t.features[n].properties:h?t.properties:{},l=f?t.features[n].bbox:h?t.bbox:void 0,d=f?t.features[n].id:h?t.id:void 0,c=!!s&&"GeometryCollection"===s.type,a=c?s.geometries.length:1,o=0;o<a;o++)if(null!==(i=c?s.geometries[o]:s))switch(i.type){case"Point":case"LineString":case"MultiPoint":case"Polygon":case"MultiLineString":case"MultiPolygon":e(i,p,u,l,d);break;case"GeometryCollection":for(r=0;r<i.geometries.length;r++)e(i.geometries[r],p,u,l,d);break;default:throw new Error("Unknown Geometry Type")}else e(null,p,u,l,d);p++}}function V(t,e,n){var r=n;return B(t,function(t,o,i,a,s){r=0===o&&void 0===n?t:e(r,t,o,i,a,s)}),r}function z(t,n){B(t,function(t,r,o,i,a){var s=null===t?null:t.type;switch(s){case null:case"Point":case"LineString":case"Polygon":return void n(e(t,o,{bbox:i,id:a}),r,0)}var c;switch(s){case"MultiPoint":c="Point";break;case"MultiLineString":c="LineString";break;case"MultiPolygon":c="Polygon"}t.coordinates.forEach(function(t,i){n(e({type:c,coordinates:t},o),r,i)})})}function j(t,e,n){var r=n;return z(t,function(t,o,i){r=0===o&&0===i&&void 0===n?t:e(r,t,o,i)}),r}function Y(t,e){z(t,function(t,n,r){var o=0;if(t.geometry){var i=t.geometry.type;"Point"!==i&&"MultiPoint"!==i&&R(t,function(i,a,c,u,l,d){var p=s([i,a],t.properties);return e(p,n,r,d,o),o++,a})}})}function X(t,e,n){var r=n,o=!1;return Y(t,function(t,i,a,s,c){r=!1===o&&void 0===n?t:e(r,t,i,a,s,c),o=!0}),r}function W(t,e){if(!t)throw new Error("geojson is required");z(t,function(t,n,r){if(null!==t.geometry){var o=t.geometry.type,i=t.geometry.coordinates;switch(o){case"LineString":e(t,n,r,0,0);break;case"Polygon":for(var a=0;a<i.length;a++)e(s(i[a],t.properties),n,r,a)}}})}function $(t,e,n){var r=n;return W(t,function(t,o,i,a){r=0===o&&void 0===n?t:e(r,t,o,i,a)}),r}function H(t){var e=[1/0,1/0,-1/0,-1/0];return T(t,function(t){e[0]>t[0]&&(e[0]=t[0]),e[1]>t[1]&&(e[1]=t[1]),e[2]<t[0]&&(e[2]=t[0]),e[3]<t[1]&&(e[3]=t[1])}),e}function K(t){if(!t)throw new Error("obj is required");var e=J(t);if(e.length>1&&E(e[0])&&E(e[1]))return e;throw new Error("Coordinate is not a valid Point")}function J(t){if(!t)throw new Error("obj is required");var e;if(t.length?e=t:t.coordinates?e=t.coordinates:t.geometry&&t.geometry.coordinates&&(e=t.geometry.coordinates),e)return Z(e),e;throw new Error("No valid coordinates")}function Z(t){if(t.length>1&&E(t[0])&&E(t[1]))return!0;if(Array.isArray(t[0])&&t[0].length)return Z(t[0]);throw new Error("coordinates must only contain numbers")}function Q(t,e,n){if(!e||!n)throw new Error("type and name required");if(!t||t.type!==e)throw new Error("Invalid input to "+n+": must be a "+e+", given "+t.type)}function tt(t,e,n){if(!t)throw new Error("No feature passed");if(!n)throw new Error(".featureOf() requires a name");if(!t||"Feature"!==t.type||!t.geometry)throw new Error("Invalid input to "+n+", Feature with geometry required");if(!t.geometry||t.geometry.type!==e)throw new Error("Invalid input to "+n+": must be a "+e+", given "+t.geometry.type)}function et(t,e,n){if(!t)throw new Error("No featureCollection passed");if(!n)throw new Error(".collectionOf() requires a name");if(!t||"FeatureCollection"!==t.type)throw new Error("Invalid input to "+n+", FeatureCollection required");for(var r=0;r<t.features.length;r++){var o=t.features[r];if(!o||"Feature"!==o.type||!o.geometry)throw new Error("Invalid input to "+n+", Feature with geometry required");if(!o.geometry||o.geometry.type!==e)throw new Error("Invalid input to "+n+": must be a "+e+", given "+o.geometry.type)}}function nt(t){if(!t)throw new Error("geojson is required");if(void 0!==t.geometry)return t.geometry;if(t.coordinates||t.geometries)return t;throw new Error("geojson must be a valid Feature or Geometry Object")}function rt(){throw new Error("invariant.getGeomType has been deprecated in v5.0 in favor of invariant.getType")}function ot(t,e){if(!t)throw new Error((e||"geojson")+" is required");if(t.geometry&&t.geometry.type)return t.geometry.type;if(t.type)return t.type;throw new Error((e||"geojson")+" is invalid")}function it(t,e,n){n=n||{};for(var r=Object.keys(Wu),o=0;o<r.length;o++){var i=r[o],a=n[i];a=void 0!==a&&null!==a?a:Wu[i],$u[i]=a}$u.verbose&&console.log("MarchingSquaresJS-isoContours: computing isocontour for "+e);var s=pt(st(t,e));return"function"==typeof $u.successCallback&&$u.successCallback(s),s}function at(t,e,n){return(t-e)/(n-e)}function st(t,e){for(var n=t.length-1,r=t[0].length-1,o={rows:n,cols:r,cells:[]},i=0;i<n;++i){o.cells[i]=[];for(var a=0;a<r;++a){var s=0,c=t[i+1][a],u=t[i+1][a+1],l=t[i][a+1],d=t[i][a];if(!(isNaN(c)||isNaN(u)||isNaN(l)||isNaN(d))){s|=c>=e?8:0,s|=u>=e?4:0,s|=l>=e?2:0,s|=d>=e?1:0;var p=!1;if(5===s||10===s){var f=(c+u+l+d)/4;5===s&&f<e?(s=10,p=!0):10===s&&f<e&&(s=5,p=!0)}if(0!==s&&15!==s){var h,g,m,y;h=g=m=y=.5,1===s?(m=1-at(e,c,d),g=1-at(e,l,d)):2===s?(g=at(e,d,l),y=1-at(e,u,l)):3===s?(m=1-at(e,c,d),y=1-at(e,u,l)):4===s?(h=at(e,c,u),y=at(e,l,u)):5===s?(h=at(e,c,u),y=at(e,l,u),g=1-at(e,l,d),m=1-at(e,c,d)):6===s?(g=at(e,d,l),h=at(e,c,u)):7===s?(m=1-at(e,c,d),h=at(e,c,u)):8===s?(m=at(e,d,c),h=1-at(e,u,c)):9===s?(g=1-at(e,l,d),h=1-at(e,u,c)):10===s?(h=1-at(e,u,c),y=1-at(e,u,l),g=at(e,d,l),m=at(e,d,c)):11===s?(h=1-at(e,u,c),y=1-at(e,u,l)):12===s?(m=at(e,d,c),y=at(e,l,u)):13===s?(g=1-at(e,l,d),y=at(e,l,u)):14===s?(m=at(e,d,c),g=at(e,d,l)):console.log("MarchingSquaresJS-isoContours: Illegal cval detected: "+s),o.cells[i][a]={cval:s,flipped:p,top:h,right:y,bottom:g,left:m}}}}}return o}function ct(t){return 5===t.cval||10===t.cval}function ut(t){return 0===t.cval||15===t.cval}function lt(t){ut(t)||5===t.cval||10===t.cval||(t.cval=15)}function dt(t,e){return"top"===e?[t.top,1]:"bottom"===e?[t.bottom,0]:"right"===e?[1,t.right]:"left"===e?[0,t.left]:void 0}function pt(t){var e=[],n=0;t.rows,t.cols;return t.cells.forEach(function(r,o){r.forEach(function(r,i){if(void 0!==r&&!ct(r)&&!ut(r)){var a=ft(t.cells,o,i),s=!1;if("mergeable"===a.info)for(var c=a.path[a.path.length-1][0],u=a.path[a.path.length-1][1],l=n-1;l>=0;l--)if(Math.abs(e[l][0][0]-c)<=1e-7&&Math.abs(e[l][0][1]-u)<=1e-7){for(var d=a.path.length-2;d>=0;--d)e[l].unshift(a.path[d]);s=!0;break}s||(e[n++]=a.path)}})}),e}function ft(t,e,n){var r,o,i,a=t.length,s=[],c=[0,0,1,1,0,0,0,0,-1,0,1,1,-1,0,-1,0],u=[0,-1,0,0,1,1,1,1,0,-1,0,0,0,-1,0,0],l=["none","left","bottom","left","right","none","bottom","left","top","top","none","top","right","right","bottom","none"],d=["none","bottom","right","right","top","top","top","top","left","bottom","right","right","left","bottom","left","none"],p=(t[e][n],t[e][n]),f=p.cval,i=l[f],h=dt(p,i);s.push([n+h[0],e+h[1]]),i=d[f],h=dt(p,i),s.push([n+h[0],e+h[1]]),lt(p);for(var g=n+c[f],m=e+u[f],y=f;g>=0&&m>=0&&m<a&&(g!=n||m!=e)&&void 0!==(p=t[m][g]);){if(0===(f=p.cval)||15===f)return{path:s,info:"mergeable"};i=d[f],r=c[f],o=u[f],5!==f&&10!==f||(5===f?p.flipped?-1===u[y]?(i="left",r=-1,o=0):(i="right",r=1,o=0):-1===c[y]&&(i="bottom",r=0,o=-1):10===f&&(p.flipped?-1===c[y]?(i="top",r=0,o=1):(i="bottom",r=0,o=-1):1===u[y]&&(i="left",r=-1,o=0))),h=dt(p,i),s.push([g+h[0],m+h[1]]),lt(p),g+=r,m+=o,y=f}return{path:s,info:"closed"}}function ht(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.zProperty||"elevation",r=e.flip,o=e.flags;et(t,"Point","input must contain Points");for(var i=gt(t,r),a=[],s=0;s<i.length;s++){for(var c=i[s],u=[],l=0;l<c.length;l++){var d=c[l];d.properties[n]?u.push(d.properties[n]):u.push(0),!0===o&&(d.properties.matrixPosition=[s,l])}a.push(u)}return a}function gt(t,e){var n={};return q(t,function(t){var e=J(t)[1];n[e]||(n[e]=[]),n[e].push(t)}),Object.keys(n).map(function(t){return n[t].sort(function(t,e){return J(t)[0]-J(e)[0]})}).sort(function(t,n){return e?J(t[0])[1]-J(n[0])[1]:J(n[0])[1]-J(t[0])[1]})}function mt(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.zProperty||"elevation",o=n.commonProperties||{},i=n.breaksProperties||[];if(et(t,"Point","Input must contain Points"),!e)throw new Error("breaks is required");if(!Array.isArray(e))throw new Error("breaks must be an Array");if(!C(o))throw new Error("commonProperties must be an Object");if(!Array.isArray(i))throw new Error("breaksProperties must be an Array");var a=ht(t,{zProperty:r,flip:!0});return u(vt(yt(a,e,r,o,i),a,t))}function yt(t,e,n,r,o){for(var i=[],a=1;a<e.length;a++){var s=Number(e[a]),c=Object.assign({},r,o[a]);c[n]=s;var u=l(it(t,s),c);i.push(u)}return i}function vt(t,e,n){var r=H(n),o=r[2]-r[0],i=r[3]-r[1],a=r[0],s=r[1],c=e[0].length-1,u=e.length-1,l=o/c,d=i/u,p=function(t){t[0]=t[0]*l+a,t[1]=t[1]*d+s};return t.forEach(function(t){T(t,p)}),t}function _t(t,e,n,r,o){for(n=n||0,r=r||t.length-1,o=o||wt;r>n;){if(r-n>600){var i=r-n+1,a=e-n+1,s=Math.log(i),c=.5*Math.exp(2*s/3),u=.5*Math.sqrt(s*c*(i-c)/i)*(a-i/2<0?-1:1);_t(t,e,Math.max(n,Math.floor(e-a*c/i+u)),Math.min(r,Math.floor(e+(i-a)*c/i+u)),o)}var l=t[e],d=n,p=r;for(bt(t,n,e),o(t[r],l)>0&&bt(t,n,r);d<p;){for(bt(t,d,p),d++,p--;o(t[d],l)<0;)d++;for(;o(t[p],l)>0;)p--}0===o(t[n],l)?bt(t,n,p):(p++,bt(t,p,r)),p<=e&&(n=p+1),e<=p&&(r=p-1)}}function bt(t,e,n){var r=t[e];t[e]=t[n],t[n]=r}function wt(t,e){return t<e?-1:t>e?1:0}function xt(t,e){if(!(this instanceof xt))return new xt(t,e);this._maxEntries=Math.max(4,t||9),this._minEntries=Math.max(2,Math.ceil(.4*this._maxEntries)),e&&this._initFormat(e),this.clear()}function Et(t,e,n){if(!n)return e.indexOf(t);for(var r=0;r<e.length;r++)if(n(t,e[r]))return r;return-1}function Ct(t,e){St(t,0,t.children.length,e,t)}function St(t,e,n,r,o){o||(o=Rt(null)),o.minX=1/0,o.minY=1/0,o.maxX=-1/0,o.maxY=-1/0;for(var i,a=e;a<n;a++)i=t.children[a],It(o,t.leaf?r(i):i);return o}function It(t,e){return t.minX=Math.min(t.minX,e.minX),t.minY=Math.min(t.minY,e.minY),t.maxX=Math.max(t.maxX,e.maxX),t.maxY=Math.max(t.maxY,e.maxY),t}function Pt(t,e){return t.minX-e.minX}function At(t,e){return t.minY-e.minY}function Nt(t){return(t.maxX-t.minX)*(t.maxY-t.minY)}function Mt(t){return t.maxX-t.minX+(t.maxY-t.minY)}function Lt(t,e){return(Math.max(e.maxX,t.maxX)-Math.min(e.minX,t.minX))*(Math.max(e.maxY,t.maxY)-Math.min(e.minY,t.minY))}function Dt(t,e){var n=Math.max(t.minX,e.minX),r=Math.max(t.minY,e.minY),o=Math.min(t.maxX,e.maxX),i=Math.min(t.maxY,e.maxY);return Math.max(0,o-n)*Math.max(0,i-r)}function Ot(t,e){return t.minX<=e.minX&&t.minY<=e.minY&&e.maxX<=t.maxX&&e.maxY<=t.maxY}function Tt(t,e){return e.minX<=t.maxX&&e.minY<=t.maxY&&e.maxX>=t.minX&&e.maxY>=t.minY}function Rt(t){return{children:t,height:1,leaf:!0,minX:1/0,minY:1/0,maxX:-1/0,maxY:-1/0}}function Ft(t,e,n,r,o){for(var i,a=[e,n];a.length;)n=a.pop(),e=a.pop(),n-e<=r||(i=e+Math.ceil((n-e)/r/2)*r,Hu(t,i,e,n,o),a.push(e,i,i,n))}function Gt(t,e){return e={exports:{}},t(e,e.exports),e.exports}function qt(t,e,n){var r=t*e,o=Zu*t,i=o-t,a=o-i,s=t-a,c=Zu*e,u=c-e,l=c-u,d=e-l,p=r-a*l,f=p-s*l,h=f-a*d,g=s*d-h;return n?(n[0]=g,n[1]=r,n):[g,r]}function kt(t,e){var n=t+e,r=n-t,o=n-r,i=e-r,a=t-o,s=a+i;return s?[s,n]:[n]}function Ut(t,e){var n=0|t.length,r=0|e.length;if(1===n&&1===r)return kt(t[0],e[0]);var o,i,a=n+r,s=new Array(a),c=0,u=0,l=0,d=Math.abs,p=t[u],f=d(p),h=e[l],g=d(h);f<g?(i=p,(u+=1)<n&&(p=t[u],f=d(p))):(i=h,(l+=1)<r&&(h=e[l],g=d(h))),u<n&&f<g||l>=r?(o=p,(u+=1)<n&&(p=t[u],f=d(p))):(o=h,(l+=1)<r&&(h=e[l],g=d(h)));for(var m,y,v,_,b,w=o+i,x=w-o,E=i-x,C=E,S=w;u<n&&l<r;)f<g?(o=p,(u+=1)<n&&(p=t[u],f=d(p))):(o=h,(l+=1)<r&&(h=e[l],g=d(h))),i=C,w=o+i,x=w-o,E=i-x,E&&(s[c++]=E),m=S+w,y=m-S,v=m-y,_=w-y,b=S-v,C=b+_,S=m;for(;u<n;)o=p,i=C,w=o+i,x=w-o,E=i-x,E&&(s[c++]=E),m=S+w,y=m-S,v=m-y,_=w-y,b=S-v,C=b+_,S=m,(u+=1)<n&&(p=t[u]);for(;l<r;)o=h,i=C,w=o+i,x=w-o,E=i-x,E&&(s[c++]=E),m=S+w,y=m-S,v=m-y,_=w-y,b=S-v,C=b+_,S=m,(l+=1)<r&&(h=e[l]);return C&&(s[c++]=C),S&&(s[c++]=S),c||(s[c++]=0),s.length=c,s}function Bt(t,e,n){var r=t+e,o=r-t,i=r-o,a=e-o,s=t-i;return n?(n[0]=s+a,n[1]=r,n):[s+a,r]}function Vt(t,e){var n=t.length;if(1===n){var r=Ju(t[0],e);return r[0]?r:[r[1]]}var o=new Array(2*n),i=[.1,.1],a=[.1,.1],s=0;Ju(t[0],e,i),i[0]&&(o[s++]=i[0]);for(var c=1;c<n;++c){Ju(t[c],e,a);var u=i[1];tl(u,a[0],i),i[0]&&(o[s++]=i[0]);var l=a[1],d=i[1],p=l+d,f=p-l,h=d-f;i[1]=p,h&&(o[s++]=h)}return i[1]&&(o[s++]=i[1]),0===s&&(o[s++]=0),o.length=s,o}function zt(t,e){var n=t+e,r=n-t,o=n-r,i=e-r,a=t-o,s=a+i;return s?[s,n]:[n]}function jt(t,e){var n=0|t.length,r=0|e.length;if(1===n&&1===r)return zt(t[0],-e[0]);var o,i,a=n+r,s=new Array(a),c=0,u=0,l=0,d=Math.abs,p=t[u],f=d(p),h=-e[l],g=d(h);f<g?(i=p,(u+=1)<n&&(p=t[u],f=d(p))):(i=h,(l+=1)<r&&(h=-e[l],g=d(h))),u<n&&f<g||l>=r?(o=p,(u+=1)<n&&(p=t[u],f=d(p))):(o=h,(l+=1)<r&&(h=-e[l],g=d(h)));for(var m,y,v,_,b,w=o+i,x=w-o,E=i-x,C=E,S=w;u<n&&l<r;)f<g?(o=p,(u+=1)<n&&(p=t[u],f=d(p))):(o=h,(l+=1)<r&&(h=-e[l],g=d(h))),i=C,w=o+i,x=w-o,E=i-x,E&&(s[c++]=E),m=S+w,y=m-S,v=m-y,_=w-y,b=S-v,C=b+_,S=m;for(;u<n;)o=p,i=C,w=o+i,x=w-o,E=i-x,E&&(s[c++]=E),m=S+w,y=m-S,v=m-y,_=w-y,b=S-v,C=b+_,S=m,(u+=1)<n&&(p=t[u]);for(;l<r;)o=h,i=C,w=o+i,x=w-o,E=i-x,E&&(s[c++]=E),m=S+w,y=m-S,v=m-y,_=w-y,b=S-v,C=b+_,S=m,(l+=1)<r&&(h=-e[l]);return C&&(s[c++]=C),S&&(s[c++]=S),c||(s[c++]=0),s.length=c,s}function Yt(t){var e=t.length;if(e<3){for(var n=new Array(e),r=0;r<e;++r)n[r]=r;return 2===e&&t[0][0]===t[1][0]&&t[0][1]===t[1][1]?[0]:n}for(var o=new Array(e),r=0;r<e;++r)o[r]=r;o.sort(function(e,n){var r=t[e][0]-t[n][0];return r||t[e][1]-t[n][1]});for(var i=[o[0],o[1]],a=[o[0],o[1]],r=2;r<e;++r){for(var s=o[r],c=t[s],u=i.length;u>1&&il(t[i[u-2]],t[i[u-1]],c)<=0;)u-=1,i.pop();for(i.push(s),u=a.length;u>1&&il(t[a[u-2]],t[a[u-1]],c)>=0;)u-=1,a.pop();a.push(s)}for(var n=new Array(a.length+i.length-2),l=0,r=0,d=i.length;r<d;++r)n[l++]=i[r];for(var p=a.length-2;p>0;--p)n[l++]=a[p];return n}function Xt(t,e){if(!(this instanceof Xt))return new Xt(t,e);if(this.data=t||[],this.length=this.data.length,this.compare=e||Wt,this.length>0)for(var n=(this.length>>1)-1;n>=0;n--)this._down(n)}function Wt(t,e){return t<e?-1:t>e?1:0}function $t(t,e,n){e=Math.max(0,void 0===e?2:e),n=n||0;for(var r,o=ne(t),i=Ku(16,["[0]","[1]","[0]","[1]"]).load(t),a=[],s=0;s<o.length;s++){var c=o[s];i.remove(c),r=re(c,r),a.push(r)}var u=Ku(16);for(s=0;s<a.length;s++)u.insert(ee(a[s]));for(var l=e*e,d=n*n;a.length;){var p=a.shift(),f=p.p,h=p.next.p,g=oe(f,h);if(!(g<d)){var m=g/l;c=Ht(i,p.prev.p,f,h,p.next.next.p,m,u),c&&Math.min(oe(c,f),oe(c,h))<=m&&(a.push(p),a.push(re(c,p)),i.remove(c),u.remove(p),u.insert(ee(p)),u.insert(ee(p.next)))}}p=r;var y=[];do{y.push(p.p),p=p.next}while(p!==r);return y.push(p.p),y}function Ht(t,e,n,r,o,i,a){for(var s=new al(null,Kt),c=t.data;c;){for(var u=0;u<c.children.length;u++){var l=c.children[u],d=c.leaf?ie(l,n,r):Jt(n,r,l);d>i||s.push({node:l,dist:d})}for(;s.length&&!s.peek().node.children;){var p=s.pop(),f=p.node,h=ie(f,e,n),g=ie(f,r,o);if(p.dist<h&&p.dist<g&&Qt(n,f,a)&&Qt(r,f,a))return f}c=s.pop(),c&&(c=c.node)}return null}function Kt(t,e){return t.dist-e.dist}function Jt(t,e,n){if(Zt(t,n)||Zt(e,n))return 0;var r=ae(t[0],t[1],e[0],e[1],n.minX,n.minY,n.maxX,n.minY);if(0===r)return 0;var o=ae(t[0],t[1],e[0],e[1],n.minX,n.minY,n.minX,n.maxY);if(0===o)return 0;var i=ae(t[0],t[1],e[0],e[1],n.maxX,n.minY,n.maxX,n.maxY);if(0===i)return 0;var a=ae(t[0],t[1],e[0],e[1],n.minX,n.maxY,n.maxX,n.maxY);return 0===a?0:Math.min(r,o,i,a)}function Zt(t,e){return t[0]>=e.minX&&t[0]<=e.maxX&&t[1]>=e.minY&&t[1]<=e.maxY}function Qt(t,e,n){for(var r=Math.min(t[0],e[0]),o=Math.min(t[1],e[1]),i=Math.max(t[0],e[0]),a=Math.max(t[1],e[1]),s=n.search({minX:r,minY:o,maxX:i,maxY:a}),c=0;c<s.length;c++)if(te(s[c].p,s[c].next.p,t,e))return!1;return!0}function te(t,e,n,r){return t!==r&&e!==n&&ul(t,e,n)>0!=ul(t,e,r)>0&&ul(n,r,t)>0!=ul(n,r,e)>0}function ee(t){var e=t.p,n=t.next.p;return t.minX=Math.min(e[0],n[0]),t.minY=Math.min(e[1],n[1]),t.maxX=Math.max(e[0],n[0]),t.maxY=Math.max(e[1],n[1]),t}function ne(t){for(var e=t[0],n=t[0],r=t[0],o=t[0],i=0;i<t.length;i++){var a=t[i];a[0]<e[0]&&(e=a),a[0]>r[0]&&(r=a),a[1]<n[1]&&(n=a),a[1]>o[1]&&(o=a)}var s=[e,n,r,o],c=s.slice();for(i=0;i<t.length;i++)cl(t[i],s)||c.push(t[i]);var u=ol(c),l=[];for(i=0;i<u.length;i++)l.push(c[u[i]]);return l}function re(t,e){var n={p:t,prev:null,next:null,minX:0,minY:0,maxX:0,maxY:0};return e?(n.next=e.next,n.prev=e,e.next.prev=n,e.next=n):(n.prev=n,n.next=n),n}function oe(t,e){var n=t[0]-e[0],r=t[1]-e[1];return n*n+r*r}function ie(t,e,n){var r=e[0],o=e[1],i=n[0]-r,a=n[1]-o;if(0!==i||0!==a){var s=((t[0]-r)*i+(t[1]-o)*a)/(i*i+a*a);s>1?(r=n[0],o=n[1]):s>0&&(r+=i*s,o+=a*s)}return i=t[0]-r,a=t[1]-o,i*i+a*a}function ae(t,e,n,r,o,i,a,s){var c,u,l,d,p=n-t,f=r-e,h=a-o,g=s-i,m=t-o,y=e-i,v=p*p+f*f,_=p*h+f*g,b=h*h+g*g,w=p*m+f*y,x=h*m+g*y,E=v*b-_*_,C=E,S=E;0===E?(u=0,C=1,d=x,S=b):(u=_*x-b*w,d=v*x-_*w,u<0?(u=0,d=x,S=b):u>C&&(u=C,d=x+_,S=b)),d<0?(d=0,-w<0?u=0:-w>v?u=C:(u=-w,C=v)):d>S&&(d=S,-w+_<0?u=0:-w+_>v?u=C:(u=-w+_,C=v)),c=0===u?0:u/C,l=0===d?0:d/S;var I=(1-c)*t+c*n,P=(1-c)*e+c*r,A=(1-l)*o+l*a,N=(1-l)*i+l*s,M=A-I,L=N-P;return M*M+L*L}function se(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.concavity||1/0,r=[];if(T(t,function(t){r.push([t[0],t[1]])}),!r.length)return null;var o=ll(r,n);return o.length>3?i([o]):null}function ce(t,e,n){if("object"!=typeof(n=n||{}))throw new Error("options is invalid");var r=n.ignoreBoundary;if(!t)throw new Error("point is required");if(!e)throw new Error("polygon is required");var o=K(t),i=J(e),a=e.geometry?e.geometry.type:e.type,s=e.bbox;if(s&&!1===le(o,s))return!1;"Polygon"===a&&(i=[i]);for(var c=0,u=!1;c<i.length&&!u;c++)if(ue(o,i[c][0],r)){for(var l=!1,d=1;d<i[c].length&&!l;)ue(o,i[c][d],!r)&&(l=!0),d++;l||(u=!0)}return u}function ue(t,e,n){var r=!1;e[0][0]===e[e.length-1][0]&&e[0][1]===e[e.length-1][1]&&(e=e.slice(0,e.length-1));for(var o=0,i=e.length-1;o<e.length;i=o++){var a=e[o][0],s=e[o][1],c=e[i][0],u=e[i][1];if(t[1]*(a-c)+s*(c-t[0])+u*(t[0]-a)==0&&(a-t[0])*(c-t[0])<=0&&(s-t[1])*(u-t[1])<=0)return!n;s>t[1]!=u>t[1]&&t[0]<(c-a)*(t[1]-s)/(u-s)+a&&(r=!r)}return r}function le(t,e){return e[0]<=t[0]&&e[1]<=t[1]&&e[2]>=t[0]&&e[3]>=t[1]}function de(t,e){var n=[];return B(e,function(e){q(t,function(t){ce(t,e)&&n.push(t)})}),u(n)}function pe(t,e){if("FeatureCollection"!==t.type)throw new Error("points must be a FeatureCollection");var n=!1;return u(me(t.features.map(function(t){var r={x:t.geometry.coordinates[0],y:t.geometry.coordinates[1]};return e?r.z=t.properties[e]:3===t.geometry.coordinates.length&&(n=!0,r.z=t.geometry.coordinates[2]),r})).map(function(t){var e=[t.a.x,t.a.y],r=[t.b.x,t.b.y],o=[t.c.x,t.c.y],a={};return n?(e.push(t.a.z),r.push(t.b.z),o.push(t.c.z)):a={a:t.a.z,b:t.b.z,c:t.c.z},i([[e,r,o,e]],a)}))}function fe(t,e,n){this.a=t,this.b=e,this.c=n;var r,o,i=e.x-t.x,a=e.y-t.y,s=n.x-t.x,c=n.y-t.y,u=i*(t.x+e.x)+a*(t.y+e.y),l=s*(t.x+n.x)+c*(t.y+n.y),d=2*(i*(n.y-e.y)-a*(n.x-e.x));this.x=(c*u-a*l)/d,this.y=(i*l-s*u)/d,r=this.x-t.x,o=this.y-t.y,this.r=r*r+o*o}function he(t,e){return e.x-t.x}function ge(t){var e,n,r,o,i,a=t.length;t:for(;a;)for(n=t[--a],e=t[--a],r=a;r;)if(i=t[--r],o=t[--r],e===o&&n===i||e===i&&n===o){t.splice(a,2),t.splice(r,2),a-=2;continue t}}function me(t){if(t.length<3)return[];t.sort(he);for(var e,n,r,o,i,a,s=t.length-1,c=t[s].x,u=t[0].x,l=t[s].y,d=l;s--;)t[s].y<l&&(l=t[s].y),t[s].y>d&&(d=t[s].y);var p,f=u-c,h=d-l,g=f>h?f:h,m=.5*(u+c),y=.5*(d+l),v=[new fe({x:m-20*g,y:y-g,__sentinel:!0},{x:m,y:y+20*g,__sentinel:!0},{x:m+20*g,y:y-g,__sentinel:!0})],_=[],b=[];for(s=t.length;s--;){for(b.length=0,p=v.length;p--;)f=t[s].x-v[p].x,f>0&&f*f>v[p].r?(_.push(v[p]),v.splice(p,1)):(h=t[s].y-v[p].y,f*f+h*h>v[p].r||(b.push(v[p].a,v[p].b,v[p].b,v[p].c,v[p].c,v[p].a),v.splice(p,1)));for(ge(b),p=b.length;p;)n=b[--p],e=b[--p],r=t[s],o=n.x-e.x,i=n.y-e.y,a=2*(o*(r.y-n.y)-i*(r.x-n.x)),Math.abs(a)>1e-12&&v.push(new fe(e,n,r))}for(Array.prototype.push.apply(_,v),s=_.length;s--;)(_[s].a.__sentinel||_[s].b.__sentinel||_[s].c.__sentinel)&&_.splice(s,1);return _}function ye(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.units,o=K(t),i=K(e),a=b(i[1]-o[1]),s=b(i[0]-o[0]),c=b(o[1]),u=b(i[1]),l=Math.pow(Math.sin(a/2),2)+Math.pow(Math.sin(s/2),2)*Math.cos(c)*Math.cos(u);return g(2*Math.atan2(Math.sqrt(l),Math.sqrt(1-l)),r)}function ve(t){if(!t)throw new Error("geojson is required");switch(t.type){case"Feature":return _e(t);case"FeatureCollection":return we(t);case"Point":case"LineString":case"Polygon":case"MultiPoint":case"MultiLineString":case"MultiPolygon":case"GeometryCollection":return xe(t);default:throw new Error("unknown GeoJSON type")}}function _e(t){var e={type:"Feature"};return Object.keys(t).forEach(function(n){switch(n){case"type":case"properties":case"geometry":return;default:e[n]=t[n]}}),e.properties=be(t.properties),e.geometry=xe(t.geometry),e}function be(t){var e={};return t?(Object.keys(t).forEach(function(n){var r=t[n];"object"==typeof r?null===r?e[n]=null:r.length?e[n]=r.map(function(t){return t}):e[n]=be(r):e[n]=r}),e):e}function we(t){var e={type:"FeatureCollection"};return Object.keys(t).forEach(function(n){switch(n){case"type":case"features":return;default:e[n]=t[n]}}),e.features=t.features.map(function(t){return _e(t)}),e}function xe(t){var e={type:t.type};return t.bbox&&(e.bbox=t.bbox),"GeometryCollection"===t.type?(e.geometries=t.geometries.map(function(t){return xe(t)}),e):(e.coordinates=Ee(t.coordinates),e)}function Ee(t){return"object"!=typeof t[0]?t.slice():t.map(function(t){return Ee(t)})}function Ce(t,e){function n(t,e){e.length&&e.pop();for(var n=u[t<0?~t:t],r=0,o=n.length;r<o;++r)e.push(c(n[r],r));t<0&&hl(e,o)}function r(t){return c(t)}function o(t){for(var e=[],r=0,o=t.length;r<o;++r)n(t[r],e);return e.length<2&&e.push(e[0]),e}function i(t){for(var e=o(t);e.length<4;)e.push(e[0]);return e}function a(t){return t.map(i)}function s(t){var e,n=t.type;switch(n){case"GeometryCollection":return{type:n,geometries:t.geometries.map(s)};case"Point":e=r(t.coordinates);break;case"MultiPoint":e=t.coordinates.map(r);break;case"LineString":e=o(t.arcs);break;case"MultiLineString":e=t.arcs.map(o);break;case"Polygon":e=a(t.arcs);break;case"MultiPolygon":e=t.arcs.map(a);break;default:return null}return{type:n,coordinates:e}}var c=fl(t.transform),u=t.arcs;return s(e)}function Se(t){for(var e,n=-1,r=t.length,o=t[r-1],i=0;++n<r;)e=o,o=t[n],i+=e[0]*o[1]-e[1]*o[0];return Math.abs(i)}function Ie(t,e){function n(t){switch(t.type){case"GeometryCollection":t.geometries.forEach(n);break;case"Polygon":r(t.arcs);break;case"MultiPolygon":t.arcs.forEach(r)}}function r(t){t.forEach(function(e){e.forEach(function(e){(i[e=e<0?~e:e]||(i[e]=[])).push(t)})}),a.push(t)}function o(e){return Se(Ce(t,{type:"Polygon",arcs:[e]}).coordinates[0])}var i={},a=[],s=[];return e.forEach(n),a.forEach(function(t){if(!t._){var e=[],n=[t];for(t._=1,s.push(e);t=n.pop();)e.push(t),t.forEach(function(t){t.forEach(function(t){i[t<0?~t:t].forEach(function(t){t._||(t._=1,n.push(t))})})})}}),a.forEach(function(t){delete t._}),{type:"MultiPolygon",arcs:s.map(function(e){var n,r=[];if(e.forEach(function(t){t.forEach(function(t){t.forEach(function(t){i[t<0?~t:t].length<2&&r.push(t)})})}),r=gl(t,r),(n=r.length)>1)for(var a,s,c=1,u=o(r[0]);c<n;++c)(a=o(r[c]))>u&&(s=r[0],r[0]=r[c],r[c]=s,u=a);return r})}}function Pe(t,e,n,r){Ae(t,e,n),Ae(t,e,e+r),Ae(t,e+r,n)}function Ae(t,e,n){for(var r,o=e+(n---e>>1);e<o;++e,--n)r=t[e],t[e]=t[n],t[n]=r}function Ne(t){return null==t?{type:null}:("FeatureCollection"===t.type?Me:"Feature"===t.type?Le:De)(t)}function Me(t){var e={type:"GeometryCollection",geometries:t.features.map(Le)};return null!=t.bbox&&(e.bbox=t.bbox),e}function Le(t){var e,n=De(t.geometry);null!=t.id&&(n.id=t.id),null!=t.bbox&&(n.bbox=t.bbox);for(e in t.properties){n.properties=t.properties;break}return n}function De(t){if(null==t)return{type:null};var e="GeometryCollection"===t.type?{type:"GeometryCollection",geometries:t.geometries.map(De)}:"Point"===t.type||"MultiPoint"===t.type?{type:t.type,coordinates:t.coordinates}:{type:t.type,arcs:t.coordinates};return null!=t.bbox&&(e.bbox=t.bbox),e}function Oe(t){var e,n=t[0],r=t[1];return r<n&&(e=n,n=r,r=e),n+31*r}function Te(t,e){
var n,r=t[0],o=t[1],i=e[0],a=e[1];return o<r&&(n=r,r=o,o=n),a<i&&(n=i,i=a,a=n),r===i&&o===a}function Re(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.mutate;if("FeatureCollection"!==ot(t))throw new Error("geojson must be a FeatureCollection");if(!t.features.length)throw new Error("geojson is empty");!1!==n&&void 0!==n||(t=ve(t));var r=[],o=$(t,function(t,e){var n=Ge(t,e);return n||(r.push(t),e)});return o&&r.push(o),r.length?1===r.length?r[0]:l(r.map(function(t){return t.coordinates})):null}function Fe(t){return t[0].toString()+","+t[1].toString()}function Ge(t,e){var n,r=t.geometry.coordinates,o=e.geometry.coordinates,i=Fe(r[0]),a=Fe(r[r.length-1]),c=Fe(o[0]),u=Fe(o[o.length-1]);if(i===u)n=o.concat(r.slice(1));else if(c===a)n=r.concat(o.slice(1));else if(i===c)n=r.slice(1).reverse().concat(o);else{if(a!==u)return null;n=r.concat(o.reverse().slice(1))}return s(n)}function qe(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.mutate;if("FeatureCollection"!==ot(t))throw new Error("geojson must be a FeatureCollection");if(!t.features.length)throw new Error("geojson is empty");!1!==n&&void 0!==n||(t=ve(t));var r=[];z(t,function(t){r.push(t.geometry)});var o=Dl({geoms:f(r).geometry});return ml(o,o.objects.geoms.geometries)}function ke(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.mutate;if("FeatureCollection"!==ot(t))throw new Error("geojson must be a FeatureCollection");if(!t.features.length)throw new Error("geojson is empty");!1!==n&&void 0!==n||(t=ve(t));var r=Ue(t);if(!r)throw new Error("geojson must be homogenous");switch(r){case"LineString":return Re(t,e);case"Polygon":return qe(t,e);default:throw new Error(r+" is not supported")}}function Ue(t){var e={};z(t,function(t){e[t.geometry.type]=!0});var n=Object.keys(e);return 1===n.length?n[0]:null}function Be(t,n){if(n=n||{},!C(n))throw new Error("options is invalid");if(!t)throw new Error("points is required");var r=n.maxEdge||1/0;if(!E(r))throw new Error("maxEdge is invalid");var o=Ve(t),i=pe(o);if(i.features=i.features.filter(function(t){var e=t.geometry.coordinates[0][0],o=t.geometry.coordinates[0][1],i=t.geometry.coordinates[0][2],a=ye(e,o,n),s=ye(o,i,n),c=ye(e,i,n);return a<=r&&s<=r&&c<=r}),i.features.length<1)return null;var a=ke(i,n);return 1===a.coordinates.length&&(a.coordinates=a.coordinates[0],a.type="Polygon"),e(a)}function Ve(t){var e=[],n={};return q(t,function(t){if(t.geometry){var r=t.geometry.coordinates.join("-");n.hasOwnProperty(r)||(e.push(t),n[r]=!0)}}),u(e)}function ze(t,e,n,r){var o=Ku(6),i=e.features.map(function(t){return{minX:t.geometry.coordinates[0],minY:t.geometry.coordinates[1],maxX:t.geometry.coordinates[0],maxY:t.geometry.coordinates[1],property:t.properties[n]}});return o.load(i),t.features.forEach(function(t){t.properties||(t.properties={});var e=H(t),n=o.search({minX:e[0],minY:e[1],maxX:e[2],maxY:e[3]}),i=[];n.forEach(function(e){ce([e.minX,e.minY],t)&&i.push(e.property)}),t.properties[r]=i}),t}function je(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.mutate;if(!t)throw new Error("geojson is required");return!1!==n&&void 0!==n||(t=ve(t)),T(t,function(t){var e=t[0],n=t[1];t[0]=n,t[1]=e}),t}function Ye(t,n){var r="object"==typeof n?n.mutate:n;if(!t)throw new Error("geojson is required");var o=ot(t),i=[];switch(o){case"LineString":i=Xe(t);break;case"MultiLineString":case"Polygon":J(t).forEach(function(t){i.push(Xe(t))});break;case"MultiPolygon":J(t).forEach(function(t){var e=[];t.forEach(function(t){e.push(Xe(t))}),i.push(e)});break;case"Point":return t;case"MultiPoint":var a={};J(t).forEach(function(t){var e=t.join("-");a.hasOwnProperty(e)||(i.push(t),a[e]=!0)});break;default:throw new Error(o+" geometry not supported")}return t.coordinates?!0===r?(t.coordinates=i,t):{type:o,coordinates:i}:!0===r?(t.geometry.coordinates=i,t):e({type:o,coordinates:i},t.properties,t.bbox,t.id)}function Xe(t){var e=J(t);if(2===e.length&&!We(e[0],e[1]))return e;var n,r,o,i=[],a=e.length-1;i.push(e[0]);for(var s=1;s<a;s++)n=e[s-1],r=e[s],o=e[s+1],$e(n,o,r)||i.push(r);return i.push(o),i}function We(t,e){return t[0]===e[0]&&t[1]===e[1]}function $e(t,e,n){var r=n[0],o=n[1],i=t[0],a=t[1],s=e[0],c=e[1],u=r-i,l=o-a,d=s-i,p=c-a;return 0==u*p-l*d&&(Math.abs(d)>=Math.abs(p)?d>0?i<=r&&r<=s:s<=r&&r<=i:p>0?a<=o&&o<=c:c<=o&&o<=a)}/*
 (c) 2013, Vladimir Agafonkin
 Simplify.js, a high-performance JS polyline simplification library
 mourner.github.io/simplify-js
*/
function He(t,e){var n=t.x-e.x,r=t.y-e.y;return n*n+r*r}function Ke(t,e,n){var r=e.x,o=e.y,i=n.x-r,a=n.y-o;if(0!==i||0!==a){var s=((t.x-r)*i+(t.y-o)*a)/(i*i+a*a);s>1?(r=n.x,o=n.y):s>0&&(r+=i*s,o+=a*s)}return i=t.x-r,a=t.y-o,i*i+a*a}function Je(t,e){for(var n,r=t[0],o=[r],i=1,a=t.length;i<a;i++)n=t[i],He(n,r)>e&&(o.push(n),r=n);return r!==n&&o.push(n),o}function Ze(t,e,n,r,o){for(var i,a=r,s=e+1;s<n;s++){var c=Ke(t[s],t[e],t[n]);c>a&&(i=s,a=c)}a>r&&(i-e>1&&Ze(t,e,i,r,o),o.push(t[i]),n-i>1&&Ze(t,i,n,r,o))}function Qe(t,e){var n=t.length-1,r=[t[0]];return Ze(t,0,n,e,r),r.push(t[n]),r}function tn(t,e,n){if(t.length<=2)return t;var r=void 0!==e?e*e:1;return t=n?t:Je(t,r),t=Qe(t,r)}function en(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=void 0!==e.tolerance?e.tolerance:1,r=e.highQuality||!1,o=e.mutate||!1;if(!t)throw new Error("geojson is required");if(n&&n<0)throw new Error("invalid tolerance");return!0!==o&&(t=ve(t)),B(t,function(t){nn(t,n,r)}),t}function nn(t,e,n){var r=t.type;if("Point"===r||"MultiPoint"===r)return t;Ye(t,!0);var o=t.coordinates;switch(r){case"LineString":t.coordinates=rn(o,e,n);break;case"MultiLineString":t.coordinates=o.map(function(t){return rn(t,e,n)});break;case"Polygon":t.coordinates=on(o,e,n);break;case"MultiPolygon":t.coordinates=o.map(function(t){return on(t,e,n)})}return t}function rn(t,e,n){return tn(t.map(function(t){return{x:t[0],y:t[1],z:t[2]}}),e,n).map(function(t){return t.z?[t.x,t.y,t.z]:[t.x,t.y]})}function on(t,e,n){return t.map(function(t){var r=t.map(function(t){return{x:t[0],y:t[1]}});if(r.length<4)throw new Error("invalid polygon");for(var o=tn(r,e,n).map(function(t){return[t.x,t.y]});!an(o);)e-=.01*e,o=tn(r,e,n).map(function(t){return[t.x,t.y]});return o[o.length-1][0]===o[0][0]&&o[o.length-1][1]===o[0][1]||o.push(o[0]),o})}function an(t){return!(t.length<3)&&!(3===t.length&&t[2][0]===t[0][0]&&t[2][1]===t[0][1])}function sn(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.resolution||1e4,r=e.sharpness||.85;if(!t)throw new Error("line is required");if(!E(n))throw new Error("resolution must be an number");if(!E(r))throw new Error("sharpness must be an number");for(var o=[],i=new Ol({points:nt(t).coordinates.map(function(t){return{x:t[0],y:t[1]}}),duration:n,sharpness:r}),a=0;a<i.duration;a+=10){var c=i.pos(a);Math.floor(a/100)%2==0&&o.push([c.x,c.y])}return s(o,t.properties)}function cn(t,e,n,r){return t=ve(t),e=ve(e),q(t,function(t){t.properties||(t.properties={}),q(e,function(e){void 0===t.properties[r]&&ce(t,e)&&(t.properties[r]=e.properties[n])})}),t}function un(t,e){if(!t)throw new Error("featurecollection is required");if(null===e||void 0===e)throw new Error("num is required");if("number"!=typeof e)throw new Error("num must be a number");return u(ln(t.features,e))}function ln(t,e){for(var n,r,o=t.slice(0),i=t.length,a=i-e;i-- >a;)r=Math.floor((i+1)*Math.random()),n=o[r],o[r]=o[i],o[i]=n;return o.slice(a)}function dn(t){S(t);var e=Number(t[0]),n=Number(t[1]),r=Number(t[2]),o=Number(t[3]);if(6===t.length)throw new Error("@turf/bbox-polygon does not support BBox with 6 positions");var a=[e,n];return i([[a,[r,n],[r,o],[e,o],a]])}function pn(t){return dn(H(t))}function fn(t){var e=t[0],n=t[1],r=t[2],o=t[3];if(ye(t.slice(0,2),[r,n])>=ye(t.slice(0,2),[e,o])){var i=(n+o)/2;return[e,i-(r-e)/2,r,i+(r-e)/2]}var a=(e+r)/2;return[a-(o-n)/2,n,a+(o-n)/2,o]}function hn(t,e,n,o){if(o=o||{},!C(o))throw new Error("options is invalid");var i=o.units,a=o.properties,s=K(t),c=b(s[0]),u=b(s[1]),l=b(n),d=m(e,i),p=Math.asin(Math.sin(u)*Math.cos(d)+Math.cos(u)*Math.sin(d)*Math.cos(l));return r([_(c+Math.atan2(Math.sin(l)*Math.sin(d)*Math.cos(u),Math.cos(d)-Math.sin(u)*Math.sin(p))),_(p)],a)}function gn(t,e,n){n=n||{};var r=n.steps||64,o=n.properties;if(!t)throw new Error("center is required");if(!e)throw new Error("radius is required");if("object"!=typeof n)throw new Error("options must be an object");if("number"!=typeof r)throw new Error("steps must be a number");r=r||64,o=o||t.properties||{};for(var a=[],s=0;s<r;s++)a.push(hn(t,e,-360*s/r,n).geometry.coordinates);return a.push(a[0]),i([a],o)}function mn(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");if(!0===n.final)return yn(t,e);var r=K(t),o=K(e),i=b(r[0]),a=b(o[0]),s=b(r[1]),c=b(o[1]),u=Math.sin(a-i)*Math.cos(c),l=Math.cos(s)*Math.sin(c)-Math.sin(s)*Math.cos(c)*Math.cos(a-i);return _(Math.atan2(u,l))}function yn(t,e){var n=mn(e,t);return n=(n+180)%360}function vn(t,e){return hn(t,ye(t,e)/2,mn(t,e))}function _n(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.properties;if(!t)throw new Error("geojson is required");var o=H(t);return r([(o[0]+o[2])/2,(o[1]+o[3])/2],n)}function bn(t,e){var n=0,o=0,i=0;return T(t,function(t){n+=t[0],o+=t[1],i++},!0),r([n/i,o/i],e)}function wn(t,e){switch(ot(t)){case"Point":return t;case"Polygon":var n=[];T(t,function(t){n.push(t)});var o,i,a,s,c,u,l,d,p=bn(t,e),f=p.geometry.coordinates,h=0,g=0,m=0,y=n.map(function(t){return[t[0]-f[0],t[1]-f[1]]});for(o=0;o<n.length-1;o++)i=y[o],s=i[0],u=i[1],a=y[o+1],c=a[0],l=a[1],d=s*l-c*u,m+=d,h+=(s+c)*d,g+=(u+l)*d;if(0===m)return p;var v=.5*m,_=1/(6*v);return r([f[0]+_*h,f[1]+_*g],e);default:var b=se(t);return b?wn(b,e):bn(t,e)}}function xn(t){function n(t,e,n){n?r[e].coordinates=r[e].coordinates.concat(t.geometry.coordinates):r[e].coordinates.push(t.geometry.coordinates),r[e].properties.push(t.properties)}var r={MultiPoint:{coordinates:[],properties:[]},MultiLineString:{coordinates:[],properties:[]},MultiPolygon:{coordinates:[],properties:[]}},o=Object.keys(r).reduce(function(t,e){return t[e.replace("Multi","")]=e,t},{});return q(t,function(t){t.geometry&&(r[t.geometry.type]?n(t,t.geometry.type,!0):o[t.geometry.type]&&n(t,o[t.geometry.type],!1))}),u(Object.keys(r).filter(function(t){return r[t].coordinates.length}).sort().map(function(t){return e({type:t,coordinates:r[t].coordinates},{collectedProperties:r[t].properties})}))}function En(t){var e=[];return"FeatureCollection"===t.type?q(t,function(t){T(t,function(n){e.push(r(n,t.properties))})}):T(t,function(n){e.push(r(n,t.properties))}),u(e)}function Cn(t,e,n){n=n||2;var r=e&&e.length,o=r?e[0]*n:t.length,i=Sn(t,0,o,n,!0),a=[];if(!i)return a;var s,c,u,l,d,p,f;if(r&&(i=Dn(t,e,i,n)),t.length>80*n){s=u=t[0],c=l=t[1];for(var h=n;h<o;h+=n)d=t[h],p=t[h+1],d<s&&(s=d),p<c&&(c=p),d>u&&(u=d),p>l&&(l=p);f=Math.max(u-s,l-c),f=0!==f?1/f:0}return Pn(i,a,n,s,c,f),a}function Sn(t,e,n,r,o){var i,a;if(o===Zn(t,e,n,r)>0)for(i=e;i<n;i+=r)a=Hn(i,t[i],t[i+1],a);else for(i=n-r;i>=e;i-=r)a=Hn(i,t[i],t[i+1],a);return a&&zn(a,a.next)&&(Kn(a),a=a.next),a}function In(t,e){if(!t)return t;e||(e=t);var n,r=t;do{if(n=!1,r.steiner||!zn(r,r.next)&&0!==Vn(r.prev,r,r.next))r=r.next;else{if(Kn(r),(r=e=r.prev)===r.next)break;n=!0}}while(n||r!==e);return e}function Pn(t,e,n,r,o,i,a){if(t){!a&&i&&Fn(t,r,o,i);for(var s,c,u=t;t.prev!==t.next;)if(s=t.prev,c=t.next,i?Nn(t,r,o,i):An(t))e.push(s.i/n),e.push(t.i/n),e.push(c.i/n),Kn(t),t=c.next,u=c.next;else if((t=c)===u){a?1===a?(t=Mn(t,e,n),Pn(t,e,n,r,o,i,2)):2===a&&Ln(t,e,n,r,o,i):Pn(In(t),e,n,r,o,i,1);break}}}function An(t){var e=t.prev,n=t,r=t.next;if(Vn(e,n,r)>=0)return!1;for(var o=t.next.next;o!==t.prev;){if(Un(e.x,e.y,n.x,n.y,r.x,r.y,o.x,o.y)&&Vn(o.prev,o,o.next)>=0)return!1;o=o.next}return!0}function Nn(t,e,n,r){var o=t.prev,i=t,a=t.next;if(Vn(o,i,a)>=0)return!1;for(var s=o.x<i.x?o.x<a.x?o.x:a.x:i.x<a.x?i.x:a.x,c=o.y<i.y?o.y<a.y?o.y:a.y:i.y<a.y?i.y:a.y,u=o.x>i.x?o.x>a.x?o.x:a.x:i.x>a.x?i.x:a.x,l=o.y>i.y?o.y>a.y?o.y:a.y:i.y>a.y?i.y:a.y,d=qn(s,c,e,n,r),p=qn(u,l,e,n,r),f=t.nextZ;f&&f.z<=p;){if(f!==t.prev&&f!==t.next&&Un(o.x,o.y,i.x,i.y,a.x,a.y,f.x,f.y)&&Vn(f.prev,f,f.next)>=0)return!1;f=f.nextZ}for(f=t.prevZ;f&&f.z>=d;){if(f!==t.prev&&f!==t.next&&Un(o.x,o.y,i.x,i.y,a.x,a.y,f.x,f.y)&&Vn(f.prev,f,f.next)>=0)return!1;f=f.prevZ}return!0}function Mn(t,e,n){var r=t;do{var o=r.prev,i=r.next.next;!zn(o,i)&&jn(o,r,r.next,i)&&Xn(o,i)&&Xn(i,o)&&(e.push(o.i/n),e.push(r.i/n),e.push(i.i/n),Kn(r),Kn(r.next),r=t=i),r=r.next}while(r!==t);return r}function Ln(t,e,n,r,o,i){var a=t;do{for(var s=a.next.next;s!==a.prev;){if(a.i!==s.i&&Bn(a,s)){var c=$n(a,s);return a=In(a,a.next),c=In(c,c.next),Pn(a,e,n,r,o,i),void Pn(c,e,n,r,o,i)}s=s.next}a=a.next}while(a!==t)}function Dn(t,e,n,r){var o,i,a,s,c,u=[];for(o=0,i=e.length;o<i;o++)a=e[o]*r,s=o<i-1?e[o+1]*r:t.length,c=Sn(t,a,s,r,!1),c===c.next&&(c.steiner=!0),u.push(kn(c));for(u.sort(On),o=0;o<u.length;o++)Tn(u[o],n),n=In(n,n.next);return n}function On(t,e){return t.x-e.x}function Tn(t,e){if(e=Rn(t,e)){var n=$n(e,t);In(n,n.next)}}function Rn(t,e){var n,r=e,o=t.x,i=t.y,a=-1/0;do{if(i<=r.y&&i>=r.next.y&&r.next.y!==r.y){var s=r.x+(i-r.y)*(r.next.x-r.x)/(r.next.y-r.y);if(s<=o&&s>a){if(a=s,s===o){if(i===r.y)return r;if(i===r.next.y)return r.next}n=r.x<r.next.x?r:r.next}}r=r.next}while(r!==e);if(!n)return null;if(o===a)return n.prev;var c,u=n,l=n.x,d=n.y,p=1/0;for(r=n.next;r!==u;)o>=r.x&&r.x>=l&&o!==r.x&&Un(i<d?o:a,i,l,d,i<d?a:o,i,r.x,r.y)&&((c=Math.abs(i-r.y)/(o-r.x))<p||c===p&&r.x>n.x)&&Xn(r,t)&&(n=r,p=c),r=r.next;return n}function Fn(t,e,n,r){var o=t;do{null===o.z&&(o.z=qn(o.x,o.y,e,n,r)),o.prevZ=o.prev,o.nextZ=o.next,o=o.next}while(o!==t);o.prevZ.nextZ=null,o.prevZ=null,Gn(o)}function Gn(t){var e,n,r,o,i,a,s,c,u=1;do{for(n=t,t=null,i=null,a=0;n;){for(a++,r=n,s=0,e=0;e<u&&(s++,r=r.nextZ);e++);for(c=u;s>0||c>0&&r;)0!==s&&(0===c||!r||n.z<=r.z)?(o=n,n=n.nextZ,s--):(o=r,r=r.nextZ,c--),i?i.nextZ=o:t=o,o.prevZ=i,i=o;n=r}i.nextZ=null,u*=2}while(a>1);return t}function qn(t,e,n,r,o){return t=32767*(t-n)*o,e=32767*(e-r)*o,t=16711935&(t|t<<8),t=252645135&(t|t<<4),t=858993459&(t|t<<2),t=1431655765&(t|t<<1),e=16711935&(e|e<<8),e=252645135&(e|e<<4),e=858993459&(e|e<<2),e=1431655765&(e|e<<1),t|e<<1}function kn(t){var e=t,n=t;do{e.x<n.x&&(n=e),e=e.next}while(e!==t);return n}function Un(t,e,n,r,o,i,a,s){return(o-a)*(e-s)-(t-a)*(i-s)>=0&&(t-a)*(r-s)-(n-a)*(e-s)>=0&&(n-a)*(i-s)-(o-a)*(r-s)>=0}function Bn(t,e){return t.next.i!==e.i&&t.prev.i!==e.i&&!Yn(t,e)&&Xn(t,e)&&Xn(e,t)&&Wn(t,e)}function Vn(t,e,n){return(e.y-t.y)*(n.x-e.x)-(e.x-t.x)*(n.y-e.y)}function zn(t,e){return t.x===e.x&&t.y===e.y}function jn(t,e,n,r){return!!(zn(t,e)&&zn(n,r)||zn(t,r)&&zn(n,e))||Vn(t,e,n)>0!=Vn(t,e,r)>0&&Vn(n,r,t)>0!=Vn(n,r,e)>0}function Yn(t,e){var n=t;do{if(n.i!==t.i&&n.next.i!==t.i&&n.i!==e.i&&n.next.i!==e.i&&jn(n,n.next,t,e))return!0;n=n.next}while(n!==t);return!1}function Xn(t,e){return Vn(t.prev,t,t.next)<0?Vn(t,e,t.next)>=0&&Vn(t,t.prev,e)>=0:Vn(t,e,t.prev)<0||Vn(t,t.next,e)<0}function Wn(t,e){var n=t,r=!1,o=(t.x+e.x)/2,i=(t.y+e.y)/2;do{n.y>i!=n.next.y>i&&n.next.y!==n.y&&o<(n.next.x-n.x)*(i-n.y)/(n.next.y-n.y)+n.x&&(r=!r),n=n.next}while(n!==t);return r}function $n(t,e){var n=new Jn(t.i,t.x,t.y),r=new Jn(e.i,e.x,e.y),o=t.next,i=e.prev;return t.next=e,e.prev=t,n.next=o,o.prev=n,r.next=n,n.prev=r,i.next=r,r.prev=i,r}function Hn(t,e,n,r){var o=new Jn(t,e,n);return r?(o.next=r.next,o.prev=r,r.next.prev=o,r.next=o):(o.prev=o,o.next=o),o}function Kn(t){t.next.prev=t.prev,t.prev.next=t.next,t.prevZ&&(t.prevZ.nextZ=t.nextZ),t.nextZ&&(t.nextZ.prevZ=t.prevZ)}function Jn(t,e,n){this.i=t,this.x=e,this.y=n,this.prev=null,this.next=null,this.z=null,this.prevZ=null,this.nextZ=null,this.steiner=!1}function Zn(t,e,n,r){for(var o=0,i=e,a=n-r;i<n;i+=r)o+=(t[a]-t[i])*(t[i+1]+t[a+1]),a=i;return o}function Qn(t){if(!t.geometry||"Polygon"!==t.geometry.type&&"MultiPolygon"!==t.geometry.type)throw new Error("input must be a Polygon or MultiPolygon");var e={type:"FeatureCollection",features:[]};return"Polygon"===t.geometry.type?e.features=tr(t.geometry.coordinates):t.geometry.coordinates.forEach(function(t){e.features=e.features.concat(tr(t))}),e}function tr(t){var e=er(t),n=Tl(e.vertices,e.holes,2),r=[],o=[];n.forEach(function(t,r){var i=n[r];o.push([e.vertices[2*i],e.vertices[2*i+1]])});for(var a=0;a<o.length;a+=3){var s=o.slice(a,a+3);s.push(o[a]),r.push(i([s]))}return r}function er(t){for(var e=t[0][0].length,n={vertices:[],holes:[],dimensions:e},r=0,o=0;o<t.length;o++){for(var i=0;i<t[o].length;i++)for(var a=0;a<e;a++)n.vertices.push(t[o][i][a]);o>0&&(r+=t[o-1].length,n.holes.push(r))}return n}function nr(t,e){if(!t)throw new Error("targetPoint is required");if(!e)throw new Error("points is required");var n,r=1/0;return q(e,function(e,o){var i=ye(t,e);i<r&&(n=ve(e),n.properties.featureIndex=o,n.properties.distanceToPoint=i,r=i)}),n}function rr(t,e,n,r,o){or(t,e,n||0,r||t.length-1,o||ar)}function or(t,e,n,r,o){for(;r>n;){if(r-n>600){var i=r-n+1,a=e-n+1,s=Math.log(i),c=.5*Math.exp(2*s/3),u=.5*Math.sqrt(s*c*(i-c)/i)*(a-i/2<0?-1:1);or(t,e,Math.max(n,Math.floor(e-a*c/i+u)),Math.min(r,Math.floor(e+(i-a)*c/i+u)),o)}var l=t[e],d=n,p=r;for(ir(t,n,e),o(t[r],l)>0&&ir(t,n,r);d<p;){for(ir(t,d,p),d++,p--;o(t[d],l)<0;)d++;for(;o(t[p],l)>0;)p--}0===o(t[n],l)?ir(t,n,p):(p++,ir(t,p,r)),p<=e&&(n=p+1),e<=p&&(r=p-1)}}function ir(t,e,n){var r=t[e];t[e]=t[n],t[n]=r}function ar(t,e){return t<e?-1:t>e?1:0}function sr(t,e){if(!(this instanceof sr))return new sr(t,e);this._maxEntries=Math.max(4,t||9),this._minEntries=Math.max(2,Math.ceil(.4*this._maxEntries)),e&&this._initFormat(e),this.clear()}function cr(t,e,n){if(!n)return e.indexOf(t);for(var r=0;r<e.length;r++)if(n(t,e[r]))return r;return-1}function ur(t,e){lr(t,0,t.children.length,e,t)}function lr(t,e,n,r,o){o||(o=br(null)),o.minX=1/0,o.minY=1/0,o.maxX=-1/0,o.maxY=-1/0;for(var i,a=e;a<n;a++)i=t.children[a],dr(o,t.leaf?r(i):i);return o}function dr(t,e){return t.minX=Math.min(t.minX,e.minX),t.minY=Math.min(t.minY,e.minY),t.maxX=Math.max(t.maxX,e.maxX),t.maxY=Math.max(t.maxY,e.maxY),t}function pr(t,e){return t.minX-e.minX}function fr(t,e){return t.minY-e.minY}function hr(t){return(t.maxX-t.minX)*(t.maxY-t.minY)}function gr(t){return t.maxX-t.minX+(t.maxY-t.minY)}function mr(t,e){return(Math.max(e.maxX,t.maxX)-Math.min(e.minX,t.minX))*(Math.max(e.maxY,t.maxY)-Math.min(e.minY,t.minY))}function yr(t,e){var n=Math.max(t.minX,e.minX),r=Math.max(t.minY,e.minY),o=Math.min(t.maxX,e.maxX),i=Math.min(t.maxY,e.maxY);return Math.max(0,o-n)*Math.max(0,i-r)}function vr(t,e){return t.minX<=e.minX&&t.minY<=e.minY&&e.maxX<=t.maxX&&e.maxY<=t.maxY}function _r(t,e){return e.minX<=t.maxX&&e.minY<=t.maxY&&e.maxX>=t.minX&&e.maxY>=t.minY}function br(t){return{children:t,height:1,leaf:!0,minX:1/0,minY:1/0,maxX:-1/0,maxY:-1/0}}function wr(t,e,n,r,o){for(var i,a=[e,n];a.length;)n=a.pop(),e=a.pop(),n-e<=r||(i=e+Math.ceil((n-e)/r/2)*r,rr(t,i,e,n,o),a.push(e,i,i,n))}function xr(t){var e=sr(t);return e.insert=function(t){if(Array.isArray(t)){var e=t;t=Er(e),t.bbox=e}else t.bbox=t.bbox?t.bbox:Cr(t);return sr.prototype.insert.call(this,t)},e.load=function(t){var e=[];return Array.isArray(t)?t.forEach(function(t){var n=Er(t);n.bbox=t,e.push(n)}):q(t,function(t){t.bbox=t.bbox?t.bbox:Cr(t),e.push(t)}),sr.prototype.load.call(this,e)},e.remove=function(t){if(Array.isArray(t)){var e=t;t=Er(e),t.bbox=e}return sr.prototype.remove.call(this,t)},e.clear=function(){return sr.prototype.clear.call(this)},e.search=function(t){return{type:"FeatureCollection",features:sr.prototype.search.call(this,this.toBBox(t))}},e.collides=function(t){return sr.prototype.collides.call(this,this.toBBox(t))},e.all=function(){return{type:"FeatureCollection",features:sr.prototype.all.call(this)}},e.toJSON=function(){return sr.prototype.toJSON.call(this)},e.fromJSON=function(t){return sr.prototype.fromJSON.call(this,t)},e.toBBox=function(t){var e;return e=t.bbox?t.bbox:Array.isArray(t)&&4===t.length?t:Cr(t),{minX:e[0],minY:e[1],maxX:e[2],maxY:e[3]}},e}function Er(t){var e=[t[0],t[1]],n=[t[0],t[3]],r=[t[2],t[3]];return{type:"Feature",bbox:t,properties:{},geometry:{type:"Polygon",coordinates:[[e,[t[2],t[1]],r,n,e]]}}}function Cr(t){var e=[1/0,1/0,-1/0,-1/0];return T(t,function(t){e[0]>t[0]&&(e[0]=t[0]),e[1]>t[1]&&(e[1]=t[1]),e[2]<t[0]&&(e[2]=t[0]),e[3]<t[1]&&(e[3]=t[1])}),e}function Sr(t){if(!t)throw new Error("geojson is required");var e=[];return z(t,function(t){Ir(t,e)}),u(e)}function Ir(t,e){var n=[],r=t.geometry;switch(r.type){case"Polygon":n=J(r);break;case"LineString":n=[J(r)]}n.forEach(function(n){Pr(n,t.properties).forEach(function(t){t.id=e.length,e.push(t)})})}function Pr(t,e){var n=[];return t.reduce(function(t,r){var o=s([t,r],e);return o.bbox=Ar(t,r),n.push(o),r}),n}function Ar(t,e){var n=t[0],r=t[1],o=e[0],i=e[1];return[n<o?n:o,r<i?r:i,n>o?n:o,r>i?r:i]}function Nr(t,n){var r={},o=[];if("LineString"===t.type&&(t=e(t)),"LineString"===n.type&&(n=e(n)),"Feature"===t.type&&"Feature"===n.type&&"LineString"===t.geometry.type&&"LineString"===n.geometry.type&&2===t.geometry.coordinates.length&&2===n.geometry.coordinates.length){var i=Mr(t,n);return i&&o.push(i),u(o)}var a=xr();return a.load(Sr(n)),q(Sr(t),function(t){q(a.search(t),function(e){var n=Mr(t,e);if(n){var i=J(n).join(",");r[i]||(r[i]=!0,o.push(n))}})}),u(o)}function Mr(t,e){var n=J(t),o=J(e);if(2!==n.length)throw new Error("<intersects> line1 must only contain 2 coordinates");if(2!==o.length)throw new Error("<intersects> line2 must only contain 2 coordinates");var i=n[0][0],a=n[0][1],s=n[1][0],c=n[1][1],u=o[0][0],l=o[0][1],d=o[1][0],p=o[1][1],f=(p-l)*(s-i)-(d-u)*(c-a),h=(d-u)*(a-l)-(p-l)*(i-u),g=(s-i)*(a-l)-(c-a)*(i-u);if(0===f)return null;var m=h/f,y=g/f;if(m>=0&&m<=1&&y>=0&&y<=1){return r([i+m*(s-i),a+m*(c-a)])}return null}function Lr(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var o=t.geometry?t.geometry.type:t.type;if("LineString"!==o&&"MultiLineString"!==o)throw new Error("lines must be LineString or MultiLineString");var i=r([1/0,1/0],{dist:1/0}),a=0;return z(t,function(t){for(var o=J(t),c=0;c<o.length-1;c++){var u=r(o[c]);u.properties.dist=ye(e,u,n);var l=r(o[c+1]);l.properties.dist=ye(e,l,n);var d=ye(u,l,n),p=Math.max(u.properties.dist,l.properties.dist),f=mn(u,l),h=hn(e,p,f+90,n),g=hn(e,p,f-90,n),m=Nr(s([h.geometry.coordinates,g.geometry.coordinates]),s([u.geometry.coordinates,l.geometry.coordinates])),y=null;m.features.length>0&&(y=m.features[0],y.properties.dist=ye(e,y,n),y.properties.location=a+ye(u,y,n)),u.properties.dist<i.properties.dist&&(i=u,i.properties.index=c,i.properties.location=a),l.properties.dist<i.properties.dist&&(i=l,i.properties.index=c+1,i.properties.location=a+d),y&&y.properties.dist<i.properties.dist&&(i=y,i.properties.index=c),a+=d}}),i}function Dr(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.final;if(!t)throw new Error("start point is required");if(!e)throw new Error("end point is required");var o;return o=r?Or(K(e),K(t)):Or(K(t),K(e)),o>180?-(360-o):o}function Or(t,e){var n=b(t[1]),r=b(e[1]),o=b(e[0]-t[0]);o>Math.PI&&(o-=2*Math.PI),o<-Math.PI&&(o+=2*Math.PI);var i=Math.log(Math.tan(r/2+Math.PI/4)/Math.tan(n/2+Math.PI/4));return(_(Math.atan2(o,i))+360)%360}function Tr(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.units;if(!t)throw new Error("from point is required");if(!e)throw new Error("to point is required");var o=K(t),i=K(e);return i[0]+=i[0]-o[0]>180?-360:o[0]-i[0]>180?360:0,w(Rr(o,i),"meters",r)}function Rr(t,e,n){n=void 0===n?Uu:Number(n);var r=n,o=t[1]*Math.PI/180,i=e[1]*Math.PI/180,a=i-o,s=Math.abs(e[0]-t[0])*Math.PI/180;s>Math.PI&&(s-=2*Math.PI);var c=Math.log(Math.tan(i/2+Math.PI/4)/Math.tan(o/2+Math.PI/4)),u=Math.abs(c)>1e-11?a/c:Math.cos(o);return Math.sqrt(a*a+u*u*s*s)*r}function Fr(t,e){return qr(t,"mercator",e)}function Gr(t,e){return qr(t,"wgs84",e)}function qr(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.mutate;if(!t)throw new Error("geojson is required");return Array.isArray(t)&&E(t[0])?t="mercator"===e?kr(t):Ur(t):(!0!==r&&(t=ve(t)),T(t,function(t){var n="mercator"===e?kr(t):Ur(t);t[0]=n[0],t[1]=n[1]})),t}function kr(t){var e=Math.PI/180,n=6378137,r=20037508.342789244,o=Math.abs(t[0])<=180?t[0]:t[0]-360*Br(t[0]),i=[n*o*e,n*Math.log(Math.tan(.25*Math.PI+.5*t[1]*e))];return i[0]>r&&(i[0]=r),i[0]<-r&&(i[0]=-r),i[1]>r&&(i[1]=r),i[1]<-r&&(i[1]=-r),i}function Ur(t){var e=180/Math.PI,n=6378137;return[t[0]*e/n,(.5*Math.PI-2*Math.atan(Math.exp(-t[1]/n)))*e]}function Br(t){return t<0?-1:t>0?1:0}function Vr(t,n,o){if(o=o||{},!C(o))throw new Error("options is invalid");if(!t)throw new Error("pt is required");if(Array.isArray(t)?t=r(t):"Point"===t.type?t=e(t):tt(t,"Point","point"),!n)throw new Error("line is required");Array.isArray(n)?n=s(n):"LineString"===n.type?n=e(n):tt(n,"LineString","line");var i=1/0,a=t.geometry.coordinates;return Y(n,function(t){var e=t.geometry.coordinates[0],n=t.geometry.coordinates[1],r=zr(a,e,n,o);i>r&&(i=r)}),i}function zr(t,e,n,r){var o=r.mercator,i=!0!==o?ye(e,t,r):Xr(e,t,r),a=v(!0!==o?mn(e,t):Dr(e,t)),s=v(!0!==o?mn(e,n):Dr(e,n)),c=Math.abs(a-s);if(c>90)return i;var u=(s+180)%360,l=v(!0!==o?mn(n,t):Dr(n,t)),d=Math.abs(l-u);return d>180&&(d=Math.abs(d-360)),d>90?!0!==o?ye(t,n,r):Xr(t,n,r):!0!==o?i*Math.sin(b(c)):jr(e,n,t,r)}function jr(t,e,n,o){var i=0;(Math.abs(t[0])>=180||Math.abs(e[0])>=180||Math.abs(n[0])>=180)&&(i=t[0]>0||e[0]>0||n[0]>0?-180:180);var a=r(n),s=Fr([t[0]+i,t[1]]),c=Fr([e[0]+i,e[1]]),u=Fr([n[0]+i,n[1]]),l=Gr(Yr(s,c,u));return 0!==i&&(l[0]-=i),Tr(a,l,o)}function Yr(t,e,n){var r=t[0],o=t[1],i=e[0],a=e[1],s=n[0],c=n[1],u=i-r,l=a-o,d=u*u+l*l,p=((s-r)*u+(c-o)*l)/d;return[r+p*u,o+p*l]}function Xr(t,e,n){var r=n.units,o=0;Math.abs(t[0])>=180&&(o=t[0]>0?-180:180),Math.abs(e[0])>=180&&(o=e[0]>0?-180:180);var i=Fr([t[0]+o,t[1]]),a=Fr([e[0]+o,e[1]]),s=function(t){return t*t},c=s(i[0]-a[0])+s(i[1]-a[1]);return w(Math.sqrt(c),"meters",r)}function Wr(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.units,o=n.properties||{};if(!t)throw new Error("points is required");if(t=$r(t),!t.features.length)throw new Error("points must contain features");if(!e)throw new Error("line is required");if("LineString"!==ot(e))throw new Error("line must be a LineString");var i=1/0,a=null;return q(t,function(t){var n=Vr(t,e,{units:r});n<i&&(i=n,a=t)}),a&&(a.properties=Object.assign({dist:i},a.properties,o)),a}function $r(t){var e=[];switch(t.geometry?t.geometry.type:t.type){case"GeometryCollection":return B(t,function(t){"Point"===t.type&&e.push({type:"Feature",properties:{},geometry:t})}),{type:"FeatureCollection",features:e};case"FeatureCollection":return t.features=t.features.filter(function(t){return"Point"===t.geometry.type}),t;default:throw new Error("points must be a Point Collection")}}function Hr(t,e){var n=K(t),r=nt(e),o=r.coordinates,i=o[0];if(i.length<4)throw new Error("OuterRing of a Polygon must have 4 or more Positions.");var a=e.properties||{},s=a.a,c=a.b,u=a.c,l=n[0],d=n[1],p=i[0][0],f=i[0][1],h=void 0!==s?s:i[0][2],g=i[1][0],m=i[1][1],y=void 0!==c?c:i[1][2],v=i[2][0],_=i[2][1],b=void 0!==u?u:i[2][2];return(b*(l-p)*(d-m)+h*(l-g)*(d-_)+y*(l-v)*(d-f)-y*(l-p)*(d-_)-b*(l-g)*(d-f)-h*(l-v)*(d-m))/((l-p)*(d-m)+(l-g)*(d-_)+(l-v)*(d-f)-(l-p)*(d-_)-(l-g)*(d-f)-(l-v)*(d-m))}function Kr(t){var e,n,o={type:"FeatureCollection",features:[]};if(n="Feature"===t.type?t.geometry:t,"LineString"===n.type)e=[n.coordinates];else if("MultiLineString"===n.type)e=n.coordinates;else if("MultiPolygon"===n.type)e=[].concat.apply([],n.coordinates);else{if("Polygon"!==n.type)throw new Error("Input must be a LineString, MultiLineString, Polygon, or MultiPolygon Feature or Geometry");e=n.coordinates}return e.forEach(function(t){e.forEach(function(e){for(var n=0;n<t.length-1;n++)for(var i=n;i<e.length-1;i++){if(t===e){if(1===Math.abs(n-i))continue;if(0===n&&i===t.length-2&&t[n][0]===t[t.length-1][0]&&t[n][1]===t[t.length-1][1])continue}var a=Jr(t[n][0],t[n][1],t[n+1][0],t[n+1][1],e[i][0],e[i][1],e[i+1][0],e[i+1][1]);a&&o.features.push(r([a[0],a[1]]))}})}),o}function Jr(t,e,n,r,o,i,a,s){var c,u,l,d,p,f={x:null,y:null,onLine1:!1,onLine2:!1};return 0===(c=(s-i)*(n-t)-(a-o)*(r-e))?null!==f.x&&null!==f.y&&f:(u=e-i,l=t-o,d=(a-o)*u-(s-i)*l,p=(n-t)*u-(r-e)*l,u=d/c,l=p/c,f.x=t+u*(n-t),f.y=e+u*(r-e),u>=0&&u<=1&&(f.onLine1=!0),l>=0&&l<=1&&(f.onLine2=!0),!(!f.onLine1||!f.onLine2)&&[f.x,f.y])}function Zr(t){for(var e=Qr(t),n=_n(e),o=!1,i=0;!o&&i<e.features.length;){var a,s,c,l,d,p,f,h=e.features[i].geometry,g=!1;if("Point"===h.type)n.geometry.coordinates[0]===h.coordinates[0]&&n.geometry.coordinates[1]===h.coordinates[1]&&(o=!0);else if("MultiPoint"===h.type){var m=!1;for(f=0;!m&&f<h.coordinates.length;)n.geometry.coordinates[0]===h.coordinates[f][0]&&n.geometry.coordinates[1]===h.coordinates[f][1]&&(o=!0,m=!0),f++}else if("LineString"===h.type)for(f=0;!g&&f<h.coordinates.length-1;)a=n.geometry.coordinates[0],s=n.geometry.coordinates[1],c=h.coordinates[f][0],l=h.coordinates[f][1],d=h.coordinates[f+1][0],p=h.coordinates[f+1][1],to(a,s,c,l,d,p)&&(g=!0,o=!0),f++;else if("MultiLineString"===h.type)for(var y=0;y<h.coordinates.length;){g=!1,f=0;for(var v=h.coordinates[y];!g&&f<v.length-1;)a=n.geometry.coordinates[0],s=n.geometry.coordinates[1],c=v[f][0],l=v[f][1],d=v[f+1][0],p=v[f+1][1],to(a,s,c,l,d,p)&&(g=!0,o=!0),f++;y++}else"Polygon"!==h.type&&"MultiPolygon"!==h.type||ce(n,h)&&(o=!0);i++}if(o)return n;var _=u([]);for(i=0;i<e.features.length;i++)_.features=_.features.concat(En(e.features[i]).features);return r(nr(n,_).geometry.coordinates)}function Qr(t){return"FeatureCollection"!==t.type?u("Feature"!==t.type?[e(t)]:[t]):t}function to(t,e,n,r,o,i){return Math.sqrt((o-n)*(o-n)+(i-r)*(i-r))===Math.sqrt((t-n)*(t-n)+(e-r)*(e-r))+Math.sqrt((o-t)*(o-t)+(i-e)*(i-e))}function eo(t){return V(t,function(t,e){return t+no(e)},0)}function no(t){var e,n=0;switch(t.type){case"Polygon":return ro(t.coordinates);case"MultiPolygon":for(e=0;e<t.coordinates.length;e++)n+=ro(t.coordinates[e]);return n;case"Point":case"MultiPoint":case"LineString":case"MultiLineString":return 0;case"GeometryCollection":for(e=0;e<t.geometries.length;e++)n+=no(t.geometries[e]);return n}}function ro(t){var e=0;if(t&&t.length>0){e+=Math.abs(oo(t[0]));for(var n=1;n<t.length;n++)e-=Math.abs(oo(t[n]))}return e}function oo(t){var e,n,r,o,i,a,s,c=0,u=t.length;if(u>2){for(s=0;s<u;s++)s===u-2?(o=u-2,i=u-1,a=0):s===u-1?(o=u-1,i=0,a=1):(o=s,i=s+1,a=s+2),e=t[o],n=t[i],r=t[a],c+=(io(r[0])-io(e[0]))*Math.sin(io(n[1]));c=c*Gl*Gl/2}return c}function io(t){return t*Math.PI/180}function ao(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var o;if("Feature"===t.type)o=t.geometry.coordinates;else{if("LineString"!==t.type)throw new Error("input must be a LineString Feature or Geometry");o=t.coordinates}if(!E(e))throw new Error("distance must be a number");for(var i=0,a=0;a<o.length&&!(e>=i&&a===o.length-1);a++){if(i>=e){var s=e-i;if(s){var c=mn(o[a],o[a-1])-180;return hn(o[a],s,c,n)}return r(o[a])}i+=ye(o[a],o[a+1],n)}return r(o[o.length-1])}function so(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");if(!t)throw new Error("geojson is required");return X(t,function(t,n){var r=n.geometry.coordinates;return t+ye(r[0],r[1],e)},0)}function co(t,e,n){var r=J(n);if("LineString"!==ot(n))throw new Error("line must be a LineString");var o,i=Lr(n,t),a=Lr(n,e);o=i.properties.index<=a.properties.index?[i,a]:[a,i];for(var c=[o[0].geometry.coordinates],u=o[0].properties.index+1;u<o[1].properties.index+1;u++)c.push(r[u]);return c.push(o[1].geometry.coordinates),s(c,n.properties)}function uo(t,e,n,r){if(r=r||{},!C(r))throw new Error("options is invalid");var o,i=[];if("Feature"===t.type)o=t.geometry.coordinates;else{if("LineString"!==t.type)throw new Error("input must be a LineString Feature or Geometry");o=t.coordinates}for(var a,c,u,l=0,d=0;d<o.length&&!(e>=l&&d===o.length-1);d++){if(l>e&&0===i.length){if(!(a=e-l))return i.push(o[d]),s(i);c=mn(o[d],o[d-1])-180,u=hn(o[d],a,c,r),i.push(u.geometry.coordinates)}if(l>=n)return(a=n-l)?(c=mn(o[d],o[d-1])-180,u=hn(o[d],a,c,r),i.push(u.geometry.coordinates),s(i)):(i.push(o[d]),s(i));if(l>=e&&i.push(o[d]),d===o.length-1)return s(i);l+=ye(o[d],o[d+1],r)}return s(o[o.length-1])}function lo(t,e,n){n=n||{};var r=n.ignoreEndVertices;if(!C(n))throw new Error("invalid options");if(!t)throw new Error("pt is required");if(!e)throw new Error("line is required");for(var o=K(t),i=J(e),a=0;a<i.length-1;a++){var s=!1;if(r&&(0===a&&(s="start"),a===i.length-2&&(s="end"),0===a&&a+1===i.length-1&&(s="both")),po(i[a],i[a+1],o,s))return!0}return!1}function po(t,e,n,r){var o=n[0],i=n[1],a=t[0],s=t[1],c=e[0],u=e[1],l=n[0]-a,d=n[1]-s,p=c-a,f=u-s;return 0==l*f-d*p&&(r?"start"===r?Math.abs(p)>=Math.abs(f)?p>0?a<o&&o<=c:c<=o&&o<a:f>0?s<i&&i<=u:u<=i&&i<s:"end"===r?Math.abs(p)>=Math.abs(f)?p>0?a<=o&&o<c:c<o&&o<=a:f>0?s<=i&&i<u:u<i&&i<=s:"both"===r?Math.abs(p)>=Math.abs(f)?p>0?a<o&&o<c:c<o&&o<a:f>0?s<i&&i<u:u<i&&i<s:void 0:Math.abs(p)>=Math.abs(f)?p>0?a<=o&&o<=c:c<=o&&o<=a:f>0?s<=i&&i<=u:u<=i&&i<=s)}function fo(t,e){var n=ot(t),r=ot(e),o=nt(t),i=nt(e);switch(n){case"Point":switch(r){case"MultiPoint":return ho(o,i);case"LineString":return lo(o,i,{ignoreEndVertices:!0});case"Polygon":return ce(o,i,{ignoreBoundary:!0});default:throw new Error("feature2 "+r+" geometry not supported")}case"MultiPoint":switch(r){case"MultiPoint":return go(o,i);case"LineString":return mo(o,i);case"Polygon":return yo(o,i);default:throw new Error("feature2 "+r+" geometry not supported")}case"LineString":switch(r){case"LineString":return vo(o,i);case"Polygon":return _o(o,i);default:throw new Error("feature2 "+r+" geometry not supported")}case"Polygon":switch(r){case"Polygon":return bo(o,i);default:throw new Error("feature2 "+r+" geometry not supported")}default:throw new Error("feature1 "+n+" geometry not supported")}}function ho(t,e){var n,r=!1;for(n=0;n<e.coordinates.length;n++)if(xo(e.coordinates[n],t.coordinates)){r=!0;break}return r}function go(t,e){for(var n=0;n<t.coordinates.length;n++){for(var r=!1,o=0;o<e.coordinates.length;o++)xo(t.coordinates[n],e.coordinates[o])&&(r=!0);if(!r)return!1}return!0}function mo(t,e){for(var n=!1,r=0;r<t.coordinates.length;r++){if(!lo(t.coordinates[r],e))return!1;n||(n=lo(t.coordinates[r],e,{ignoreEndVertices:!0}))}return n}function yo(t,e){for(var n=!0,r=0;r<t.coordinates.length;r++){var o=ce(t.coordinates[1],e);if(!o){n=!1;break}o=ce(t.coordinates[1],e,{ignoreBoundary:!0})}return n&&o}function vo(t,e){for(var n=0;n<t.coordinates.length;n++)if(!lo(t.coordinates[n],e))return!1;return!0}function _o(t,e){if(!wo(H(e),H(t)))return!1;for(var n=!1,r=0;r<t.coordinates.length-1;r++){if(!ce(t.coordinates[r],e))return!1;if(n||(n=ce(t.coordinates[r],e,{ignoreBoundary:!0})),!n){n=ce(Eo(t.coordinates[r],t.coordinates[r+1]),e,{ignoreBoundary:!0})}}return n}function bo(t,e){var n=H(t);if(!wo(H(e),n))return!1;for(var r=0;r<t.coordinates[0].length;r++)if(!ce(t.coordinates[0][r],e))return!1;return!0}function wo(t,e){return!(t[0]>e[0])&&(!(t[2]<e[2])&&(!(t[1]>e[1])&&!(t[3]<e[3])))}function xo(t,e){return t[0]===e[0]&&t[1]===e[1]}function Eo(t,e){return[(t[0]+e[0])/2,(t[1]+e[1])/2]}function Co(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var o=n.mask,i=n.properties,a=[];if(null===e||void 0===e)throw new Error("cellSide is required");if(!E(e))throw new Error("cellSide is invalid");if(!t)throw new Error("bbox is required");if(!Array.isArray(t))throw new Error("bbox must be array");if(4!==t.length)throw new Error("bbox must contain 4 numbers");if(o&&-1===["Polygon","MultiPolygon"].indexOf(ot(o)))throw new Error("options.mask must be a (Multi)Polygon");for(var s=t[0],c=t[1],l=t[2],d=t[3],p=e/ye([s,c],[l,c],n),f=p*(l-s),h=e/ye([s,c],[s,d],n),g=h*(d-c),m=l-s,y=d-c,v=Math.floor(m/f),_=Math.floor(y/g),b=(m-v*f)/2,w=(y-_*g)/2,x=s+b;x<=l;){for(var S=c+w;S<=d;){var I=r([x,S],i);o?fo(I,o)&&a.push(I):a.push(I),S+=g}x+=f}return u(a)}function So(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.precision,r=e.coordinates,o=e.mutate;if(n=void 0===n||null===n||isNaN(n)?6:n,r=void 0===r||null===r||isNaN(r)?3:r,!t)throw new Error("<geojson> is required");if("number"!=typeof n)throw new Error("<precision> must be a number");if("number"!=typeof r)throw new Error("<coordinates> must be a number");!1!==o&&void 0!==o||(t=JSON.parse(JSON.stringify(t)));var i=Math.pow(10,n);return T(t,function(t){Io(t,i,r)}),t}function Io(t,e,n){
t.length>n&&t.splice(n,t.length);for(var r=0;r<t.length;r++)t[r]=Math.round(t[r]*e)/e;return t}function Po(t){if(!t)throw new Error("geojson is required");var e=[];return z(t,function(t){e.push(t)}),u(e)}function Ao(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.units,o=n.reverse;if(!t)throw new Error("geojson is required");if(e<=0)throw new Error("segmentLength must be greater than 0");var i=[];return z(t,function(t){o&&(t.geometry.coordinates=t.geometry.coordinates.reverse()),No(t,e,r,function(t){i.push(t)})}),u(i)}function No(t,e,n,r){var o=so(t,{units:n});if(o<=e)return r(t);var i=o/e;Number.isInteger(i)||(i=Math.floor(i)+1);for(var a=0;a<i;a++){r(uo(t,e*a,e*(a+1),{units:n}),a)}}function Mo(t,e,n,r){if(Lo(t,n)||Lo(t,r)||Lo(e,n)||Lo(r,n))return null;var o=t[0],i=t[1],a=e[0],s=e[1],c=n[0],u=n[1],l=r[0],d=r[1],p=(o-a)*(u-d)-(i-s)*(c-l);return 0===p?null:[((o*s-i*a)*(c-l)-(o-a)*(c*d-u*l))/p,((o*s-i*a)*(u-d)-(i-s)*(c*d-u*l))/p]}function Lo(t,e){if(!t||!e)return!1;if(t.length!==e.length)return!1;for(var n=0,r=t.length;n<r;n++)if(t[n]instanceof Array&&e[n]instanceof Array){if(!Lo(t[n],e[n]))return!1}else if(t[n]!==e[n])return!1;return!0}function Do(t,e){if(void 0===e&&(e=!0),3!=t.length)throw new Error("This function requires an array of three points [x,y]");return(t[1][0]-t[0][0])*(t[2][1]-t[0][1])-(t[1][1]-t[0][1])*(t[2][0]-t[0][0])>=0==e}function Oo(t){for(var e=0,n=0;n<t.length-1;n++)t[n][0]<t[e][0]&&(e=n);if(Do([t[(e-1).modulo(t.length-1)],t[e],t[(e+1).modulo(t.length-1)]],!0))var r=1;else var r=-1;return r}function To(t,e){if(!t||!e)return!1;if(t.length!=e.length)return!1;for(var n=0,r=t.length;n<r;n++)if(t[n]instanceof Array&&e[n]instanceof Array){if(!To(t[n],e[n]))return!1}else if(t[n]!=e[n])return!1;return!0}function Ro(t){for(var e={},n=1,r=0,o=t.length;r<o;++r){if(e.hasOwnProperty(t[r])){n=0;break}e[t[r]]=1}return n}function Fo(t){var e=[];return z(t,function(t){"Polygon"===t.geometry.type&&q(kl(t),function(n){e.push(i(n.geometry.coordinates,t.properties))})}),u(e)}function Go(t,e,n){if("object"!=typeof(n=n||{}))throw new Error("options is invalid");var r=n.properties,o=n.npoints,i=n.offset;return t=K(t),e=K(e),r=r||{},o=o||100,i=i||10,new Wl({x:t[0],y:t[1]},{x:e[0],y:e[1]},r).Arc(o,{offset:i}).json()}function qo(t,e){if(!t)throw new Error("line is required");if(!e)throw new Error("splitter is required");var n=ot(t),r=ot(e);if("LineString"!==n)throw new Error("line must be LineString");if("FeatureCollection"===r)throw new Error("splitter cannot be a FeatureCollection");if("GeometryCollection"===r)throw new Error("splitter cannot be a GeometryCollection");var o=So(e,{precision:7});switch(r){case"Point":return Uo(t,o);case"MultiPoint":return ko(t,o);case"LineString":case"MultiLineString":case"Polygon":case"MultiPolygon":return ko(t,Nr(t,o))}}function ko(t,e){var n=[],r=xr();return z(e,function(e){if(n.forEach(function(t,e){t.id=e}),n.length){var o=r.search(e);if(o.features.length){var i=Bo(e,o);n=n.filter(function(t){return t.id!==i.id}),r.remove(i),q(Uo(i,e),function(t){n.push(t),r.insert(t)})}}else n=Uo(t,e).features,n.forEach(function(t){t.bbox||(t.bbox=fn(H(t)))}),r.load(u(n))}),u(n)}function Uo(t,e){var n=[],r=J(t)[0],o=J(t)[t.geometry.coordinates.length-1];if(Vo(r,K(e))||Vo(o,K(e)))return u([t]);var i=xr(),a=Sr(t);i.load(a);var c=i.search(e);if(!c.features.length)return u([t]);var l=Bo(e,c),d=[r],p=k(a,function(t,r,o){var i=J(r)[1],a=K(e);return o===l.id?(t.push(a),n.push(s(t)),Vo(a,i)?[a]:[a,i]):(t.push(i),t)},d);return p.length>1&&n.push(s(p)),u(n)}function Bo(t,e){if(!e.features.length)throw new Error("lines must contain features");if(1===e.features.length)return e.features[0];var n,r=1/0;return q(e,function(e){var o=Lr(e,t),i=o.properties.dist;i<r&&(n=e,r=i)}),n}function Vo(t,e){return t[0]===e[0]&&t[1]===e[1]}function zo(t,e,n,r,o){if(o=o||{},!C(o))throw new Error("options is invalid");var i=o.steps,a=o.units;if(!t)throw new Error("center is required");if(!e)throw new Error("radius is required");if(void 0===n||null===n)throw new Error("bearing1 is required");if(void 0===r||null===r)throw new Error("bearing2 is required");if("object"!=typeof o)throw new Error("options must be an object");i=i||64;var c=jo(n),u=jo(r),l=t.properties;if(c===u)return s(gn(t,e,o).geometry.coordinates[0],l);for(var d=c,p=c<u?u:u+360,f=d,h=[],g=0;f<p;)h.push(hn(t,e,f,a).geometry.coordinates),g++,f=d+360*g/i;return f>p&&h.push(hn(t,e,p,a).geometry.coordinates),s(h,l)}function jo(t){var e=t%360;return e<0&&(e+=360),e}function Yo(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.properties,r=ot(t),o=J(t);if(n=n||t.properties||{},!o.length)throw new Error("polygon must contain coordinates");switch(r){case"Polygon":return Xo(o,n);case"MultiPolygon":var i=[];return o.forEach(function(t){i.push(Xo(t,n))}),u(i);default:throw new Error("geom "+r+" not supported")}}function Xo(t,e){return t.length>1?l(t,e):s(t[0],e)}function Wo(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.properties,r=e.autoComplete,o=e.orderCoords;if(!t)throw new Error("lines is required");switch(r=void 0===r||r,o=void 0===o||o,ot(t)){case"FeatureCollection":case"GeometryCollection":var i=[];return(t.features?t.features:t.geometries).forEach(function(t){i.push(J($o(t,{},r,o)))}),p(i,n)}return $o(t,n,r,o)}function $o(t,e,n,r){e=e||t.properties||{};var o=J(t),a=ot(t);if(!o.length)throw new Error("line must contain coordinates");switch(a){case"LineString":return n&&(o=Ho(o)),i([o],e);case"MultiLineString":var c=[],u=0;return o.forEach(function(t){if(n&&(t=Ho(t)),r){var e=Ko(H(s(t)));e>u?(c.unshift(t),u=e):c.push(t)}else c.push(t)}),i(c,e);default:throw new Error("geometry type "+a+" is not supported")}}function Ho(t){var e=t[0],n=e[0],r=e[1],o=t[t.length-1],i=o[0],a=o[1];return n===i&&r===a||t.push(e),t}function Ko(t){var e=t[0],n=t[1],r=t[2],o=t[3];return Math.abs(e-r)*Math.abs(n-o)}function Jo(t,e,n){var r,o,i,a,s,c=t.length,u=ti(t[0],e),l=[];for(n||(n=[]),r=1;r<c;r++){for(o=t[r-1],i=t[r],a=s=ti(i,e);;){if(!(u|a)){l.push(o),a!==s?(l.push(i),r<c-1&&(n.push(l),l=[])):r===c-1&&l.push(i);break}if(u&a)break;u?(o=Qo(o,i,u,e),u=ti(o,e)):(i=Qo(o,i,a,e),a=ti(i,e))}u=s}return l.length&&n.push(l),n}function Zo(t,e){var n,r,o,i,a,s,c;for(r=1;r<=8;r*=2){for(n=[],o=t[t.length-1],i=!(ti(o,e)&r),a=0;a<t.length;a++)s=t[a],c=!(ti(s,e)&r),c!==i&&n.push(Qo(o,s,r,e)),c&&n.push(s),o=s,i=c;if(t=n,!t.length)break}return n}function Qo(t,e,n,r){return 8&n?[t[0]+(e[0]-t[0])*(r[3]-t[1])/(e[1]-t[1]),r[3]]:4&n?[t[0]+(e[0]-t[0])*(r[1]-t[1])/(e[1]-t[1]),r[1]]:2&n?[r[2],t[1]+(e[1]-t[1])*(r[2]-t[0])/(e[0]-t[0])]:1&n?[r[0],t[1]+(e[1]-t[1])*(r[0]-t[0])/(e[0]-t[0])]:null}function ti(t,e){var n=0;return t[0]<e[0]?n|=1:t[0]>e[2]&&(n|=2),t[1]<e[1]?n|=4:t[1]>e[3]&&(n|=8),n}function ei(t,e){var n=ri(t),r=J(t),o=t.properties;switch(n){case"LineString":case"MultiLineString":var a=[];return"LineString"===n&&(r=[r]),r.forEach(function(t){$l(t,e,a)}),1===a.length?s(a[0],o):l(a,o);case"Polygon":return i(ni(r,e),o);case"MultiPolygon":return p(r.map(function(t){return ni(t,e)}),o);default:throw new Error("geometry "+n+" not supported")}}function ni(t,e){for(var n=[],r=0;r<t.length;r++){var o=$l.polygon(t[r],e);o.length>0&&(o[0][0]===o[o.length-1][0]&&o[0][1]===o[o.length-1][1]||o.push(o[0]),o.length>=4&&n.push(o))}return n}function ri(t){return t.geometry?t.geometry.type:t.type}function oi(t){return"[object Arguments]"===Object.prototype.toString.call(t)}function ii(t,e,n){return n||(n={}),t===e||(t instanceof Date&&e instanceof Date?t.getTime()===e.getTime():!t||!e||"object"!=typeof t&&"object"!=typeof e?(n.strict,t===e):ci(t,e,n))}function ai(t){return null===t||void 0===t}function si(t){return!(!t||"object"!=typeof t||"number"!=typeof t.length)&&("function"==typeof t.copy&&"function"==typeof t.slice&&!(t.length>0&&"number"!=typeof t[0]))}function ci(t,e,n){var r,o;if(ai(t)||ai(e))return!1;if(t.prototype!==e.prototype)return!1;if(oi(t))return!!oi(e)&&(t=Hl.call(t),e=Hl.call(e),ii(t,e,n));if(si(t)){if(!si(e))return!1;if(t.length!==e.length)return!1;for(r=0;r<t.length;r++)if(t[r]!==e[r])return!1;return!0}try{var i=Object.keys(t),a=Object.keys(e)}catch(t){return!1}if(i.length!==a.length)return!1;for(i.sort(),a.sort(),r=i.length-1;r>=0;r--)if(i[r]!==a[r])return!1;for(r=i.length-1;r>=0;r--)if(o=i[r],!ii(t[o],e[o],n))return!1;return typeof t==typeof e}function ui(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.tolerance||0,o=[],i=xr();i.load(Sr(t));var a;return Y(e,function(t){var e=!1;q(i.search(t),function(n){if(!1===e){var o=J(t).sort(),i=J(n).sort();ii(o,i)?(e=!0,a=a?li(a,t):t):(0===r?lo(o[0],n)&&lo(o[1],n):Lr(n,o[0]).properties.dist<=r&&Lr(n,o[1]).properties.dist<=r)?(e=!0,a=a?li(a,t):t):(0===r?lo(i[0],t)&&lo(i[1],t):Lr(t,i[0]).properties.dist<=r&&Lr(t,i[1]).properties.dist<=r)&&(a=a?li(a,n):n)}}),!1===e&&a&&(o.push(a),a=void 0)}),a&&o.push(a),u(o)}function li(t,e){var n=J(e),r=J(t),o=r[0],i=r[r.length-1],a=t.geometry.coordinates;return ii(n[0],o)?a.unshift(n[1]):ii(n[0],i)?a.push(n[1]):ii(n[1],o)?a.unshift(n[0]):ii(n[1],i)&&a.push(n[0]),t}function di(t,e,n,r,o){if(o=o||{},!C(o))throw new Error("options is invalid");if(!t)throw new Error("center is required");if(void 0===n||null===n)throw new Error("bearing1 is required");if(void 0===r||null===r)throw new Error("bearing2 is required");if(!e)throw new Error("radius is required");if("object"!=typeof o)throw new Error("options must be an object");if(pi(n)===pi(r))return gn(t,e,o);var a=J(t),s=zo(t,e,n,r,o),c=[[a]];return T(s,function(t){c[0].push(t)}),c[0].push(a),i(c)}function pi(t){var e=t%360;return e<0&&(e+=360),e}function fi(t,e,n,o){if(o=o||{},!C(o))throw new Error("options is invalid");var i=o.units,a=o.properties;if(!t)throw new Error("origin is required");if(void 0===e||null===e)throw new Error("distance is required");if(void 0===n||null===n)throw new Error("bearing is required");if(!(e>=0))throw new Error("distance must be greater than 0");var s=w(e,i,"meters"),c=K(t),u=hi(c,s,n);return u[0]+=u[0]-c[0]>180?-360:c[0]-u[0]>180?360:0,r(u,a)}function hi(t,e,n,r){r=void 0===r?Uu:Number(r);var o=e/r,i=t[0]*Math.PI/180,a=b(t[1]),s=b(n),c=o*Math.cos(s),u=a+c;Math.abs(u)>Math.PI/2&&(u=u>0?Math.PI-u:-Math.PI-u);var l=Math.log(Math.tan(u/2+Math.PI/4)/Math.tan(a/2+Math.PI/4)),d=Math.abs(l)>1e-11?c/l:Math.cos(a);return[(180*(i+o*Math.sin(s)/d)/Math.PI+540)%360-180,180*u/Math.PI]}function gi(t,e){var n,o,i,a=J(t),s=J(e);switch(ot(e)){case"Polygon":n=s[0][0],o=s[0][0],i=_i(s[0][0],s[0][s[0].length-1],a);var c=mi(s[0],a,i,void 0,n,o);n=c[0],o=c[1];break;case"MultiPolygon":n=s[0][0][0],o=s[0][0][0],i=_i(s[0][0][0],s[0][0][s[0][0].length-1],a),s.forEach(function(t){var e=mi(t[0],a,i,void 0,n,o);n=e[0],o=e[1]})}return u([r(n),r(o)])}function mi(t,e,n,r,o,i){for(var a=0;a<t.length;a++){var s=t[a],c=t[a+1];a===t.length-1&&(c=t[0]),r=_i(s,c,e),n<=0&&r>0?vi(e,s,o)||(o=s):n>0&&r<=0&&(yi(e,s,i)||(i=s)),n=r}return[o,i]}function yi(t,e,n){return _i(t,e,n)>0}function vi(t,e,n){return _i(t,e,n)<0}function _i(t,e,n){return(e[0]-t[0])*(n[1]-t[1])-(n[0]-t[0])*(e[1]-t[1])}function bi(t){if(!t)throw new Error("line is required");var e=t.geometry?t.geometry.type:t.type;if(!Array.isArray(t)&&"LineString"!==e)throw new Error("geometry must be a LineString");for(var n,r,o=J(t),i=0,a=1;a<o.length;)n=r||o[0],r=o[a],i+=(r[0]-n[0])*(r[1]+n[1]),a++;return i>0}function wi(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.reverse||!1,r=e.mutate||!1;if(!t)throw new Error("<geojson> is required");if("boolean"!=typeof n)throw new Error("<reverse> must be a boolean");if("boolean"!=typeof r)throw new Error("<mutate> must be a boolean");!1===r&&(t=ve(t));var o=[];switch(t.type){case"GeometryCollection":return B(t,function(t){xi(t,n)}),t;case"FeatureCollection":return q(t,function(t){q(xi(t,n),function(t){o.push(t)})}),u(o)}return xi(t,n)}function xi(t,e){switch("Feature"===t.type?t.geometry.type:t.type){case"GeometryCollection":return B(t,function(t){xi(t,e)}),t;case"LineString":return Ei(J(t),e),t;case"Polygon":return Ci(J(t),e),t;case"MultiLineString":return J(t).forEach(function(t){Ei(t,e)}),t;case"MultiPolygon":return J(t).forEach(function(t){Ci(t,e)}),t;case"Point":case"MultiPoint":return t}}function Ei(t,e){bi(t)===e&&t.reverse()}function Ci(t,e){bi(t[0])!==e&&t[0].reverse();for(var n=1;n<t.length;n++)bi(t[n])===e&&t[n].reverse()}function Si(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.zProperty||"elevation",r=e.flip,o=e.flags;et(t,"Point","input must contain Points");for(var i=Ii(t,r),a=[],s=0;s<i.length;s++){for(var c=i[s],u=[],l=0;l<c.length;l++){var d=c[l];d.properties[n]?u.push(d.properties[n]):u.push(0),!0===o&&(d.properties.matrixPosition=[s,l])}a.push(u)}return a}function Ii(t,e){var n={};return q(t,function(t){var e=J(t)[1];n[e]||(n[e]=[]),n[e].push(t)}),Object.keys(n).map(function(t){return n[t].sort(function(t,e){return J(t)[0]-J(e)[0]})}).sort(function(t,n){return e?J(t[0])[1]-J(n[0])[1]:J(n[0])[1]-J(t[0])[1]})}function Pi(t,e,n,r){r=r||{};for(var o=Object.keys(Kl),i=0;i<o.length;i++){var a=o[i],s=r[a];s=void 0!==s&&null!==s?s:Kl[a],Jl[a]=s}Jl.verbose&&console.log("MarchingSquaresJS-isoBands: computing isobands for ["+e+":"+(e+n)+"]");var c,u=Mi(t,e,n);return Jl.polygons?(Jl.verbose&&console.log("MarchingSquaresJS-isoBands: returning single polygons for each grid cell"),c=Fi(u)):(Jl.verbose&&console.log("MarchingSquaresJS-isoBands: returning polygon paths for entire data grid"),c=Li(u)),"function"==typeof Jl.successCallback&&Jl.successCallback(c),c}function Ai(t,e,n){return(t-e)/(n-e)}function Ni(t){return t.constructor.toString().indexOf("Array")>-1}function Mi(t,e,n){for(var r=t.length-1,o=t[0].length-1,i={rows:r,cols:o,cells:[]},a=e+Math.abs(n),s=0;s<r;++s){i.cells[s]=[];for(var c=0;c<o;++c){var u=0,l=t[s+1][c],d=t[s+1][c+1],p=t[s][c+1],f=t[s][c];if(!(isNaN(l)||isNaN(d)||isNaN(p)||isNaN(f))){u|=l<e?0:l>a?128:64,u|=d<e?0:d>a?32:16,u|=p<e?0:p>a?8:4,u|=f<e?0:f>a?2:1;var h=+u,g=0;if(17===u||18===u||33===u||34===u||38===u||68===u||72===u||98===u||102===u||132===u||136===u||137===u||152===u||153===u){var m=(l+d+p+f)/4;g=m>a?2:m<e?0:1,34===u?1===g?u=35:0===g&&(u=136):136===u?1===g?(u=35,g=4):0===g&&(u=34):17===u?1===g?(u=155,g=4):0===g&&(u=153):68===u?1===g?(u=103,g=4):0===g&&(u=102):153===u?1===g&&(u=155):102===u?1===g&&(u=103):152===u?g<2&&(u=156,g=1):137===u?g<2&&(u=139,g=1):98===u?g<2&&(u=99,g=1):38===u?g<2&&(u=39,g=1):18===u?g>0?(u=156,g=4):u=152:33===u?g>0?(u=139,g=4):u=137:72===u?g>0?(u=99,g=4):u=98:132===u&&(g>0?(u=39,g=4):u=38)}if(0!=u&&170!=u){var y,v,_,b,w,x,E,C;y=v=_=b=w=x=E=C=.5;var S=[];1===u?(_=1-Ai(e,p,f),C=1-Ai(e,l,f),S.push(gp[u])):169===u?(_=Ai(a,f,p),C=Ai(a,f,l),S.push(gp[u])):4===u?(x=1-Ai(e,d,p),b=Ai(e,f,p),S.push(fp[u])):166===u?(x=Ai(a,p,d),b=1-Ai(a,p,f),S.push(fp[u])):16===u?(w=Ai(e,p,d),v=Ai(e,l,d),S.push(pp[u])):154===u?(w=1-Ai(a,d,p),v=1-Ai(a,d,l),S.push(pp[u])):64===u?(E=Ai(e,f,l),y=1-Ai(e,d,l),S.push(yp[u])):106===u?(E=1-Ai(a,l,f),y=Ai(a,l,d),S.push(yp[u])):168===u?(b=Ai(a,f,p),_=Ai(e,f,p),C=Ai(e,f,l),E=Ai(a,f,l),S.push(hp[u]),S.push(gp[u])):2===u?(b=1-Ai(e,p,f),_=1-Ai(a,p,f),C=1-Ai(a,l,f),E=1-Ai(e,l,f),S.push(hp[u]),S.push(gp[u])):162===u?(w=Ai(a,p,d),x=Ai(e,p,d),b=1-Ai(e,p,f),_=1-Ai(a,p,f),S.push(hp[u]),S.push(gp[u])):8===u?(w=1-Ai(e,d,p),x=1-Ai(a,d,p),b=Ai(a,f,p),_=Ai(e,f,p),S.push(pp[u]),S.push(fp[u])):138===u?(w=1-Ai(e,d,p),x=1-Ai(a,d,p),y=1-Ai(a,d,l),v=1-Ai(e,d,l),S.push(pp[u]),S.push(fp[u])):32===u?(w=Ai(a,p,d),x=Ai(e,p,d),y=Ai(e,l,d),v=Ai(a,l,d),S.push(pp[u]),S.push(fp[u])):42===u?(C=1-Ai(a,l,f),E=1-Ai(e,l,f),y=Ai(e,l,d),v=Ai(a,l,d),S.push(mp[u]),S.push(yp[u])):128===u&&(C=Ai(e,f,l),E=Ai(a,f,l),y=1-Ai(a,d,l),v=1-Ai(e,d,l),S.push(mp[u]),S.push(yp[u])),5===u?(x=1-Ai(e,d,p),C=1-Ai(e,l,f),S.push(fp[u])):165===u?(x=Ai(a,p,d),C=Ai(a,f,l),S.push(fp[u])):20===u?(b=Ai(e,f,p),v=Ai(e,l,d),S.push(hp[u])):150===u?(b=1-Ai(a,p,f),v=1-Ai(a,d,l),S.push(hp[u])):80===u?(w=Ai(e,p,d),E=Ai(e,f,l),S.push(pp[u])):90===u?(w=1-Ai(a,d,p),E=1-Ai(a,l,f),S.push(pp[u])):65===u?(_=1-Ai(e,p,f),y=1-Ai(e,d,l),S.push(gp[u])):105===u?(_=Ai(a,f,p),y=Ai(a,l,d),S.push(gp[u])):160===u?(w=Ai(a,p,d),x=Ai(e,p,d),C=Ai(e,f,l),E=Ai(a,f,l),S.push(pp[u]),S.push(fp[u])):10===u?(w=1-Ai(e,d,p),x=1-Ai(a,d,p),C=1-Ai(a,l,f),E=1-Ai(e,l,f),S.push(pp[u]),S.push(fp[u])):130===u?(b=1-Ai(e,p,f),_=1-Ai(a,p,f),y=1-Ai(a,d,l),v=1-Ai(e,d,l),S.push(hp[u]),S.push(gp[u])):40===u?(b=Ai(a,f,p),_=Ai(e,f,p),y=Ai(e,l,d),v=Ai(a,l,d),S.push(hp[u]),S.push(gp[u])):101===u?(x=Ai(a,p,d),y=Ai(a,l,d),S.push(fp[u])):69===u?(x=1-Ai(e,d,p),y=1-Ai(e,d,l),S.push(fp[u])):149===u?(C=Ai(a,f,l),v=1-Ai(a,d,l),S.push(mp[u])):21===u?(C=1-Ai(e,l,f),v=Ai(e,l,d),S.push(mp[u])):86===u?(b=1-Ai(a,p,f),E=1-Ai(a,l,f),S.push(hp[u])):84===u?(b=Ai(e,f,p),E=Ai(e,f,l),S.push(hp[u])):89===u?(w=1-Ai(a,d,p),_=Ai(a,f,p),S.push(gp[u])):81===u?(w=Ai(e,p,d),_=1-Ai(e,p,f),S.push(gp[u])):96===u?(w=Ai(a,p,d),x=Ai(e,p,d),E=Ai(e,f,l),y=Ai(a,l,d),S.push(pp[u]),S.push(fp[u])):74===u?(w=1-Ai(e,d,p),x=1-Ai(a,d,p),E=1-Ai(a,l,f),y=1-Ai(e,d,l),S.push(pp[u]),S.push(fp[u])):24===u?(w=1-Ai(a,d,p),b=Ai(a,f,p),_=Ai(e,f,p),v=Ai(e,l,d),S.push(pp[u]),S.push(gp[u])):146===u?(w=Ai(e,p,d),b=1-Ai(e,p,f),_=1-Ai(a,p,f),v=1-Ai(a,d,l),S.push(pp[u]),S.push(gp[u])):6===u?(x=1-Ai(e,d,p),b=1-Ai(a,p,f),C=1-Ai(a,l,f),E=1-Ai(e,l,f),S.push(fp[u]),S.push(hp[u])):164===u?(x=Ai(a,p,d),b=Ai(e,f,p),C=Ai(e,f,l),E=Ai(a,f,l),S.push(fp[u]),S.push(hp[u])):129===u?(_=1-Ai(e,p,f),C=Ai(a,f,l),y=1-Ai(a,d,l),v=1-Ai(e,d,l),S.push(gp[u]),S.push(mp[u])):41===u?(_=Ai(a,f,p),C=1-Ai(e,l,f),y=Ai(e,l,d),v=Ai(a,l,d),S.push(gp[u]),S.push(mp[u])):66===u?(b=1-Ai(e,p,f),_=1-Ai(a,p,f),E=1-Ai(a,l,f),y=1-Ai(e,d,l),S.push(hp[u]),S.push(gp[u])):104===u?(b=Ai(a,f,p),_=Ai(e,f,p),E=Ai(e,f,l),y=Ai(a,l,d),S.push(gp[u]),S.push(vp[u])):144===u?(w=Ai(e,p,d),C=Ai(e,f,l),E=Ai(a,f,l),v=1-Ai(a,d,l),S.push(pp[u]),S.push(yp[u])):26===u?(w=1-Ai(a,d,p),C=1-Ai(a,l,f),E=1-Ai(e,l,f),v=Ai(e,l,d),S.push(pp[u]),S.push(yp[u])):36===u?(x=Ai(a,p,d),b=Ai(e,f,p),y=Ai(e,l,d),v=Ai(a,l,d),S.push(fp[u]),S.push(hp[u])):134===u?(x=1-Ai(e,d,p),b=1-Ai(a,p,f),y=1-Ai(a,d,l),v=1-Ai(e,d,l),S.push(fp[u]),S.push(hp[u])):9===u?(w=1-Ai(e,d,p),x=1-Ai(a,d,p),_=Ai(a,f,p),C=1-Ai(e,l,f),S.push(pp[u]),S.push(fp[u])):161===u?(w=Ai(a,p,d),x=Ai(e,p,d),_=1-Ai(e,p,f),C=Ai(a,f,l),S.push(pp[u]),S.push(fp[u])):37===u?(x=Ai(a,p,d),C=1-Ai(e,l,f),y=Ai(e,l,d),v=Ai(a,l,d),S.push(fp[u]),S.push(mp[u])):133===u?(x=1-Ai(e,d,p),C=Ai(a,f,l),y=1-Ai(a,d,l),v=1-Ai(e,d,l),S.push(fp[u]),S.push(mp[u])):148===u?(b=Ai(e,f,p),C=Ai(e,f,l),E=Ai(a,f,l),v=1-Ai(a,d,l),S.push(hp[u]),S.push(yp[u])):22===u?(b=1-Ai(a,p,f),C=1-Ai(a,l,f),E=1-Ai(e,l,f),v=Ai(e,l,d),S.push(hp[u]),S.push(yp[u])):82===u?(w=Ai(e,p,d),b=1-Ai(e,p,f),_=1-Ai(a,p,f),E=1-Ai(a,l,f),S.push(pp[u]),S.push(gp[u])):88===u?(w=1-Ai(a,d,p),b=Ai(a,f,p),_=Ai(e,f,p),E=Ai(e,f,l),S.push(pp[u]),S.push(gp[u])):73===u?(w=1-Ai(e,d,p),x=1-Ai(a,d,p),_=Ai(a,f,p),y=1-Ai(e,d,l),S.push(pp[u]),S.push(fp[u])):97===u?(w=Ai(a,p,d),x=Ai(e,p,d),_=1-Ai(e,p,f),y=Ai(a,l,d),S.push(pp[u]),S.push(fp[u])):145===u?(w=Ai(e,p,d),_=1-Ai(e,p,f),C=Ai(a,f,l),v=1-Ai(a,d,l),S.push(pp[u]),S.push(mp[u])):25===u?(w=1-Ai(a,d,p),_=Ai(a,f,p),C=1-Ai(e,l,f),v=Ai(e,l,d),S.push(pp[u]),S.push(mp[u])):70===u?(x=1-Ai(e,d,p),b=1-Ai(a,p,f),E=1-Ai(a,l,f),y=1-Ai(e,d,l),S.push(fp[u]),S.push(hp[u])):100===u?(x=Ai(a,p,d),b=Ai(e,f,p),E=Ai(e,f,l),y=Ai(a,l,d),S.push(fp[u]),S.push(hp[u])):34===u?(0===g?(w=1-Ai(e,d,p),x=1-Ai(a,d,p),b=Ai(a,f,p),_=Ai(e,f,p),C=Ai(e,f,l),E=Ai(a,f,l),y=1-Ai(a,d,l),v=1-Ai(e,d,l)):(w=Ai(a,p,d),x=Ai(e,p,d),b=1-Ai(e,p,f),_=1-Ai(a,p,f),C=1-Ai(a,l,f),E=1-Ai(e,l,f),y=Ai(e,l,d),v=Ai(a,l,d)),S.push(pp[u]),S.push(fp[u]),S.push(mp[u]),S.push(yp[u])):35===u?(4===g?(w=1-Ai(e,d,p),x=1-Ai(a,d,p),b=Ai(a,f,p),_=Ai(e,f,p),C=Ai(e,f,l),E=Ai(a,f,l),y=1-Ai(a,d,l),v=1-Ai(e,d,l)):(w=Ai(a,p,d),x=Ai(e,p,d),b=1-Ai(e,p,f),_=1-Ai(a,p,f),C=1-Ai(a,l,f),E=1-Ai(e,l,f),y=Ai(e,l,d),v=Ai(a,l,d)),S.push(pp[u]),S.push(fp[u]),S.push(gp[u]),S.push(yp[u])):136===u?(0===g?(w=Ai(a,p,d),x=Ai(e,p,d),b=1-Ai(e,p,f),_=1-Ai(a,p,f),C=1-Ai(a,l,f),E=1-Ai(e,l,f),y=Ai(e,l,d),v=Ai(a,l,d)):(w=1-Ai(e,d,p),x=1-Ai(a,d,p),b=Ai(a,f,p),_=Ai(e,f,p),C=Ai(e,f,l),E=Ai(a,f,l),y=1-Ai(a,d,l),v=1-Ai(e,d,l)),S.push(pp[u]),S.push(fp[u]),S.push(mp[u]),S.push(yp[u])):153===u?(0===g?(w=Ai(e,p,d),_=1-Ai(e,p,f),C=1-Ai(e,l,f),v=Ai(e,l,d)):(w=1-Ai(a,d,p),_=Ai(a,f,p),C=Ai(a,f,l),v=1-Ai(a,d,l)),S.push(pp[u]),S.push(gp[u])):102===u?(0===g?(x=1-Ai(e,d,p),b=Ai(e,f,p),E=Ai(e,f,l),y=1-Ai(e,d,l)):(x=Ai(a,p,d),b=1-Ai(a,p,f),E=1-Ai(a,l,f),y=Ai(a,l,d)),S.push(fp[u]),S.push(yp[u])):155===u?(4===g?(w=Ai(e,p,d),_=1-Ai(e,p,f),C=1-Ai(e,l,f),v=Ai(e,l,d)):(w=1-Ai(a,d,p),_=Ai(a,f,p),C=Ai(a,f,l),v=1-Ai(a,d,l)),S.push(pp[u]),S.push(mp[u])):103===u?(4===g?(x=1-Ai(e,d,p),b=Ai(e,f,p),E=Ai(e,f,l),y=1-Ai(e,d,l)):(x=Ai(a,p,d),b=1-Ai(a,p,f),E=1-Ai(a,l,f),y=Ai(a,l,d)),S.push(fp[u]),S.push(hp[u])):152===u?(0===g?(w=Ai(e,p,d),b=1-Ai(e,p,f),_=1-Ai(a,p,f),C=1-Ai(a,l,f),E=1-Ai(e,l,f),v=Ai(e,l,d)):(w=1-Ai(a,d,p),b=Ai(a,f,p),_=Ai(e,f,p),C=Ai(e,f,l),E=Ai(a,f,l),v=1-Ai(a,d,l)),S.push(pp[u]),S.push(hp[u]),S.push(gp[u])):156===u?(4===g?(w=Ai(e,p,d),b=1-Ai(e,p,f),_=1-Ai(a,p,f),C=1-Ai(a,l,f),E=1-Ai(e,l,f),v=Ai(e,l,d)):(w=1-Ai(a,d,p),b=Ai(a,f,p),_=Ai(e,f,p),C=Ai(e,f,l),E=Ai(a,f,l),v=1-Ai(a,d,l)),S.push(pp[u]),S.push(gp[u]),S.push(yp[u])):137===u?(0===g?(w=Ai(a,p,d),x=Ai(e,p,d),_=1-Ai(e,p,f),C=1-Ai(e,l,f),y=Ai(e,l,d),v=Ai(a,l,d)):(w=1-Ai(e,d,p),x=1-Ai(a,d,p),_=Ai(a,f,p),C=Ai(a,f,l),y=1-Ai(a,d,l),v=1-Ai(e,d,l)),S.push(pp[u]),S.push(fp[u]),S.push(gp[u])):139===u?(4===g?(w=Ai(a,p,d),x=Ai(e,p,d),_=1-Ai(e,p,f),C=1-Ai(e,l,f),y=Ai(e,l,d),v=Ai(a,l,d)):(w=1-Ai(e,d,p),x=1-Ai(a,d,p),_=Ai(a,f,p),C=Ai(a,f,l),y=1-Ai(a,d,l),v=1-Ai(e,d,l)),S.push(pp[u]),S.push(fp[u]),S.push(mp[u])):98===u?(0===g?(w=1-Ai(e,d,p),x=1-Ai(a,d,p),b=Ai(a,f,p),_=Ai(e,f,p),E=Ai(e,f,l),y=1-Ai(e,d,l)):(w=Ai(a,p,d),x=Ai(e,p,d),b=1-Ai(e,p,f),_=1-Ai(a,p,f),E=1-Ai(a,l,f),y=Ai(a,l,d)),S.push(pp[u]),S.push(fp[u]),S.push(yp[u])):99===u?(4===g?(w=1-Ai(e,d,p),x=1-Ai(a,d,p),b=Ai(a,f,p),_=Ai(e,f,p),E=Ai(e,f,l),y=1-Ai(e,d,l)):(w=Ai(a,p,d),x=Ai(e,p,d),b=1-Ai(e,p,f),_=1-Ai(a,p,f),E=1-Ai(a,l,f),y=Ai(a,l,d)),S.push(pp[u]),S.push(fp[u]),S.push(gp[u])):38===u?(0===g?(x=1-Ai(e,d,p),b=Ai(e,f,p),C=Ai(e,f,l),E=Ai(a,f,l),y=1-Ai(a,d,l),v=1-Ai(e,d,l)):(x=Ai(a,p,d),b=1-Ai(a,p,f),C=1-Ai(a,l,f),E=1-Ai(e,l,f),y=Ai(e,l,d),v=Ai(a,l,d)),S.push(fp[u]),S.push(mp[u]),S.push(yp[u])):39===u?(4===g?(x=1-Ai(e,d,p),b=Ai(e,f,p),C=Ai(e,f,l),E=Ai(a,f,l),y=1-Ai(a,d,l),v=1-Ai(e,d,l)):(x=Ai(a,p,d),b=1-Ai(a,p,f),C=1-Ai(a,l,f),E=1-Ai(e,l,f),y=Ai(e,l,d),v=Ai(a,l,d)),S.push(fp[u]),S.push(hp[u]),S.push(yp[u])):85===u&&(w=1,x=0,b=1,_=0,C=0,E=1,y=0,v=1),(y<0||y>1||v<0||v>1||w<0||w>1||b<0||b>1||C<0||C>1||E<0||E>1)&&console.log("MarchingSquaresJS-isoBands: "+u+" "+h+" "+l+","+d+","+p+","+f+" "+g+" "+y+" "+v+" "+w+" "+x+" "+b+" "+_+" "+C+" "+E),i.cells[s][c]={cval:u,cval_real:h,flipped:g,topleft:y,topright:v,righttop:w,rightbottom:x,bottomright:b,bottomleft:_,leftbottom:C,lefttop:E,edges:S}}}}}return i}function Li(t){for(var e=[],n=t.rows,r=t.cols,o=[],i=0;i<n;i++)for(var a=0;a<r;a++)if(void 0!==t.cells[i][a]&&t.cells[i][a].edges.length>0){var s=t.cells[i][a],c=Ti(s),u=null,l=a,d=i;null!==c&&o.push([c.p[0]+l,c.p[1]+d]);do{if(null===(u=Ri(t.cells[d][l],c.x,c.y,c.o)))break;if(o.push([u.p[0]+l,u.p[1]+d]),l+=u.x,d+=u.y,c=u,d<0||d>=n||l<0||l>=r||void 0===t.cells[d][l]){l-=u.x,d-=u.y;var p=Di(t,l,d,u.x,u.y,u.o);if(null===p)break;p.path.forEach(function(t){o.push(t)}),l=p.i,d=p.j,c=p}}while(void 0!==t.cells[d][l]&&t.cells[d][l].edges.length>0);e.push(o),o=[],t.cells[i][a].edges.length>0&&a--}return e}function Di(t,e,n,r,o,i){for(var a=t.cells[n][e],s=a.cval_real,c=e+r,u=n+o,l=[],d=!1;!d;){if(void 0===t.cells[u]||void 0===t.cells[u][c])if(u-=o,c-=r,a=t.cells[u][c],s=a.cval_real,-1===o)if(0===i)if(s&ed)l.push([c,u]),r=-1,o=0,i=0;else{if(!(s&td)){l.push([c+a.bottomright,u]),r=0,o=1,i=1,d=!0;break}l.push([c+1,u]),r=1,o=0,i=0}else{if(!(s&ed)){if(s&td){l.push([c+a.bottomright,u]),r=0,o=1,i=1,d=!0;break}l.push([c+a.bottomleft,u]),r=0,o=1,i=0,d=!0;break}l.push([c,u]),r=-1,o=0,i=0}else if(1===o)if(0===i){if(!(s&Ql)){if(s&Zl){l.push([c+a.topleft,u+1]),r=0,o=-1,i=0,d=!0;break}l.push([c+a.topright,u+1]),r=0,o=-1,i=1,d=!0;break}l.push([c+1,u+1]),r=1,o=0,i=1}else l.push([c+1,u+1]),r=1,o=0,i=1;else if(-1===r)if(0===i){if(!(s&Zl)){if(s&ed){l.push([c,u+a.leftbottom]),r=1,o=0,i=0,d=!0;break}l.push([c,u+a.lefttop]),r=1,o=0,i=1,d=!0;break}l.push([c,u+1]),r=0,o=1,i=0}else{if(!(s&Zl)){console.log("MarchingSquaresJS-isoBands: wtf");break}l.push([c,u+1]),r=0,o=1,i=0}else{if(1!==r){console.log("MarchingSquaresJS-isoBands: we came from nowhere!");break}if(0===i){if(!(s&td)){l.push([c+1,u+a.rightbottom]),r=-1,o=0,i=0,d=!0;break}l.push([c+1,u]),r=0,o=-1,i=1}else{if(!(s&td)){if(s&Ql){l.push([c+1,u+a.righttop]),r=-1,o=0,i=1;break}l.push([c+1,u+a.rightbottom]),r=-1,o=0,i=0,d=!0;break}l.push([c+1,u]),r=0,o=-1,i=1}}else if(a=t.cells[u][c],s=a.cval_real,-1===r)if(0===i)if(void 0!==t.cells[u-1]&&void 0!==t.cells[u-1][c])r=0,o=-1,i=1;else{if(!(s&ed)){l.push([c+a.bottomright,u]),r=0,o=1,i=1,d=!0;break}l.push([c,u])}else{if(!(s&Zl)){console.log("MarchingSquaresJS-isoBands: found entry from top at "+c+","+u);break}console.log("MarchingSquaresJS-isoBands: proceeding in x-direction!")}else if(1===r){if(0===i){console.log("MarchingSquaresJS-isoBands: wtf");break}if(void 0!==t.cells[u+1]&&void 0!==t.cells[u+1][c])r=0,o=1,i=0;else{if(!(s&Ql)){l.push([c+a.topleft,u+1]),r=0,o=-1,i=0,d=!0;break}l.push([c+1,u+1]),r=1,o=0,i=1}}else if(-1===o){if(1!==i){console.log("MarchingSquaresJS-isoBands: wtf");break}if(void 0!==t.cells[u][c+1])r=1,o=0,i=1;else{if(!(s&td)){l.push([c+1,u+a.righttop]),r=-1,o=0,i=1,d=!0;break}l.push([c+1,u]),r=0,o=-1,i=1}}else{if(1!==o){console.log("MarchingSquaresJS-isoBands: where did we came from???");break}if(0!==i){console.log("MarchingSquaresJS-isoBands: wtf");break}if(void 0!==t.cells[u][c-1])r=-1,o=0,i=0;else{if(!(s&Zl)){l.push([c,u+a.leftbottom]),r=1,o=0,i=0,d=!0;break}l.push([c,u+1]),r=0,o=1,i=0}}if(c+=r,u+=o,c===e&&u===n)break}return{path:l,i:c,j:u,x:r,y:o,o:i}}function Oi(t,e){delete t.edges[e];for(var n=e+1;n<t.edges.length;n++)t.edges[n-1]=t.edges[n];t.edges.pop()}function Ti(t){if(t.edges.length>0){var e=t.edges[t.edges.length-1],n=t.cval_real;switch(e){case 0:return n&Ql?{p:[1,t.righttop],x:-1,y:0,o:1}:{p:[t.topleft,1],x:0,y:-1,o:0};case 1:return n&td?{p:[t.topleft,1],x:0,y:-1,o:0}:{p:[1,t.rightbottom],x:-1,y:0,o:0};case 2:return n&td?{p:[t.bottomright,0],x:0,y:1,o:1}:{p:[t.topleft,1],x:0,y:-1,o:0};case 3:return n&ed?{p:[t.topleft,1],x:0,y:-1,o:0}:{p:[t.bottomleft,0],x:0,y:1,o:0};case 4:return n&Ql?{p:[1,t.righttop],x:-1,y:0,o:1}:{p:[t.topright,1],x:0,y:-1,o:1};case 5:return n&td?{p:[t.topright,1],x:0,y:-1,o:1}:{p:[1,t.rightbottom],x:-1,y:0,o:0};case 6:return n&td?{p:[t.bottomright,0],x:0,y:1,o:1}:{p:[t.topright,1],x:0,y:-1,o:1};case 7:return n&ed?{p:[t.topright,1],x:0,y:-1,o:1}:{p:[t.bottomleft,0],x:0,y:1,o:0};case 8:return n&td?{p:[t.bottomright,0],x:0,y:1,o:1}:{p:[1,t.righttop],x:-1,y:0,o:1};case 9:return n&ed?{p:[1,t.righttop],x:-1,y:0,o:1}:{p:[t.bottomleft,0],x:0,y:1,o:0};case 10:return n&ed?{p:[0,t.leftbottom],x:1,y:0,o:0}:{p:[1,t.righttop],x:-1,y:0,o:1};case 11:return n&Zl?{p:[1,t.righttop],x:-1,y:0,o:1}:{p:[0,t.lefttop],x:1,y:0,o:1};case 12:return n&td?{p:[t.bottomright,0],x:0,y:1,o:1}:{p:[1,t.rightbottom],x:-1,y:0,o:0};case 13:return n&ed?{p:[1,t.rightbottom],x:-1,y:0,o:0}:{p:[t.bottomleft,0],x:0,y:1,o:0};case 14:return n&ed?{p:[0,t.leftbottom],x:1,y:0,o:0}:{p:[1,t.rightbottom],x:-1,y:0,o:0};case 15:return n&Zl?{p:[1,t.rightbottom],x:-1,y:0,o:0}:{p:[0,t.lefttop],x:1,y:0,o:1};case 16:return n&td?{p:[t.bottomright,0],x:0,y:1,o:1}:{p:[0,t.leftbottom],x:1,y:0,o:0};case 17:return n&Zl?{p:[t.bottomright,0],x:0,y:1,o:1}:{p:[0,t.lefttop],x:1,y:0,o:1};case 18:return n&ed?{p:[0,t.leftbottom],x:1,y:0,o:0}:{p:[t.bottomleft,0],x:0,y:1,o:0};case 19:return n&Zl?{p:[t.bottomleft,0],x:0,y:1,o:0}:{p:[0,t.lefttop],x:1,y:0,o:1};case 20:return n&Zl?{p:[t.topleft,1],x:0,y:-1,o:0}:{p:[0,t.leftbottom],x:1,y:0,o:0};case 21:return n&Ql?{p:[0,t.leftbottom],x:1,y:0,o:0}:{p:[t.topright,1],x:0,y:-1,o:1};case 22:return n&Zl?{p:[t.topleft,1],x:0,y:-1,o:0}:{p:[0,t.lefttop],x:1,y:0,o:1};case 23:return n&Ql?{p:[0,t.lefttop],x:1,y:0,o:1}:{p:[t.topright,1],x:0,y:-1,o:1};default:console.log("MarchingSquaresJS-isoBands: edge index out of range!"),console.log(t)}}return null}function Ri(t,e,n,r){var o,i,a,s,c,u=t.cval;switch(e){case-1:switch(r){case 0:o=fp[u],a=dd[u],s=pd[u],c=fd[u];break;default:o=pp[u],a=cd[u],s=ud[u],c=ld[u]}break;case 1:switch(r){case 0:o=mp[u],a=Ed[u],s=Cd[u],c=Sd[u];break;default:o=yp[u],a=bd[u],s=wd[u],c=xd[u]}break;default:switch(n){case-1:switch(r){case 0:o=vp[u],a=nd[u],s=rd[u],c=od[u];break;default:o=_p[u],a=id[u],s=ad[u],c=sd[u]}break;case 1:switch(r){case 0:o=gp[u],a=hd[u],s=gd[u],c=md[u];break;default:o=hp[u],a=yd[u],s=vd[u],c=_d[u]}}}if(i=t.edges.indexOf(o),void 0===t.edges[i])return null;switch(Oi(t,i),u=t.cval_real,o){case 0:u&Ql?(e=t.topleft,n=1):(e=1,n=t.righttop);break;case 1:u&td?(e=1,n=t.rightbottom):(e=t.topleft,n=1);break;case 2:u&td?(e=t.topleft,n=1):(e=t.bottomright,n=0);break;case 3:u&ed?(e=t.bottomleft,n=0):(e=t.topleft,n=1);break;case 4:u&Ql?(e=t.topright,n=1):(e=1,n=t.righttop);break;case 5:u&td?(e=1,n=t.rightbottom):(e=t.topright,n=1);break;case 6:u&td?(e=t.topright,n=1):(e=t.bottomright,n=0);break;case 7:u&ed?(e=t.bottomleft,n=0):(e=t.topright,n=1);break;case 8:u&td?(e=1,n=t.righttop):(e=t.bottomright,n=0);break;case 9:u&ed?(e=t.bottomleft,n=0):(e=1,n=t.righttop);break;case 10:u&ed?(e=1,n=t.righttop):(e=0,n=t.leftbottom);break;case 11:u&Zl?(e=0,n=t.lefttop):(e=1,n=t.righttop);break;case 12:u&td?(e=1,n=t.rightbottom):(e=t.bottomright,n=0);break;case 13:u&ed?(e=t.bottomleft,n=0):(e=1,n=t.rightbottom);break;case 14:u&ed?(e=1,n=t.rightbottom):(e=0,n=t.leftbottom);break;case 15:u&Zl?(e=0,n=t.lefttop):(e=1,n=t.rightbottom);break;case 16:u&td?(e=0,n=t.leftbottom):(e=t.bottomright,n=0);break;case 17:u&Zl?(e=0,n=t.lefttop):(e=t.bottomright,n=0);break;case 18:u&ed?(e=t.bottomleft,n=0):(e=0,n=t.leftbottom);break;case 19:u&Zl?(e=0,n=t.lefttop):(e=t.bottomleft,n=0);break;case 20:u&Zl?(e=0,n=t.leftbottom):(e=t.topleft,n=1);break;case 21:u&Ql?(e=t.topright,n=1):(e=0,n=t.leftbottom);break;case 22:u&Zl?(e=0,n=t.lefttop):(e=t.topleft,n=1);break;case 23:u&Ql?(e=t.topright,n=1):(e=0,n=t.lefttop);break;default:return console.log("MarchingSquaresJS-isoBands: edge index out of range!"),console.log(t),null}return void 0!==e&&void 0!==n&&void 0!==a&&void 0!==s&&void 0!==c||(console.log("MarchingSquaresJS-isoBands: undefined value!"),console.log(t),console.log(e+" "+n+" "+a+" "+s+" "+c)),{p:[e,n],x:a,y:s,o:c}}function Fi(t){var e=[],n=0;return t.cells.forEach(function(t,r){t.forEach(function(t,o){if(void 0!==t){var i=bp[t.cval](t);"object"==typeof i&&Ni(i)?"object"==typeof i[0]&&Ni(i[0])?"object"==typeof i[0][0]&&Ni(i[0][0])?i.forEach(function(t){t.forEach(function(t){t[0]+=o,t[1]+=r}),e[n++]=t}):(i.forEach(function(t){t[0]+=o,t[1]+=r}),e[n++]=i):console.log("MarchingSquaresJS-isoBands: bandcell polygon with malformed coordinates"):console.log("MarchingSquaresJS-isoBands: bandcell polygon with null coordinates")}})}),e}function Gi(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.zProperty||"elevation",o=n.commonProperties||{},i=n.breaksProperties||[];if(et(t,"Point","Input must contain Points"),!e)throw new Error("breaks is required");if(!Array.isArray(e))throw new Error("breaks is not an Array");if(!C(o))throw new Error("commonProperties is not an Object");if(!Array.isArray(i))throw new Error("breaksProperties is not an Array");var a=Si(t,{zProperty:r,flip:!0}),s=qi(a,e,r);return s=ki(s,a,t),u(s.map(function(t,e){if(i[e]&&!C(i[e]))throw new Error("Each mappedProperty is required to be an Object");var n=Object.assign({},o,i[e]);return n[r]=t[r],p(t.groupedRings,n)}))}function qi(t,e,n){for(var r=[],o=1;o<e.length;o++){
var i=+e[o-1],a=+e[o],s=Pi(t,i,a-i),c=Ui(s),u=Bi(c),l={};l.groupedRings=u,l[n]=i+"-"+a,r.push(l)}return r}function ki(t,e,n){var r=H(n),o=r[2]-r[0],i=r[3]-r[1],a=r[0],s=r[1],c=e[0].length-1,u=e.length-1,l=o/c,d=i/u,p=function(t){t[0]=t[0]*l+a,t[1]=t[1]*d+s};return t.forEach(function(t){t.groupedRings.forEach(function(t){t.forEach(function(t){t.forEach(p)})})}),t}function Ui(t){var e=[],n=[];t.forEach(function(t){var r=eo(i([t]));n.push(r),e.push({ring:t,area:r})}),n.sort(function(t,e){return e-t});var r=[];return n.forEach(function(t){for(var n=0;n<e.length;n++)if(e[n].area===t){r.push(e[n].ring),e.splice(n,1);break}}),r}function Bi(t){for(var e=t.map(function(t){return{lrCoordinates:t,grouped:!1}}),n=[];!zi(e);)for(var r=0;r<e.length;r++)if(!e[r].grouped){var o=[];o.push(e[r].lrCoordinates),e[r].grouped=!0;for(var a=i([e[r].lrCoordinates]),s=r+1;s<e.length;s++)if(!e[s].grouped){var c=i([e[s].lrCoordinates]);Vi(c,a)&&(o.push(e[s].lrCoordinates),e[s].grouped=!0)}n.push(o)}return n}function Vi(t,e){for(var n=En(t),r=0;r<n.features.length;r++)if(!ce(n.features[r],e))return!1;return!0}function zi(t){for(var e=0;e<t.length;e++)if(!1===t[e].grouped)return!1;return!0}function ji(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.pivot,o=n.mutate;if(!t)throw new Error("geojson is required");if(void 0===e||null===e||isNaN(e))throw new Error("angle is required");return 0===e?t:(r||(r=bn(t)),!1!==o&&void 0!==o||(t=ve(t)),T(t,function(t){var n=Dr(r,t),o=n+e,i=Tr(r,t),a=J(fi(r,i,o));t[0]=a[0],t[1]=a[1]}),t)}function Yi(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.origin,o=n.mutate;if(!t)throw new Error("geojson required");if("number"!=typeof e||0===e)throw new Error("invalid factor");var i=Array.isArray(r)||"object"==typeof r;return!0!==o&&(t=ve(t)),"FeatureCollection"!==t.type||i?Xi(t,e,r):(q(t,function(n,o){t.features[o]=Xi(n,e,r)}),t)}function Xi(t,e,n){var r="Point"===ot(t);return n=Wi(t,n),1===e||r?t:(T(t,function(t){var r=Tr(n,t),o=Dr(n,t),i=r*e,a=J(fi(n,i,o));t[0]=a[0],t[1]=a[1],3===t.length&&(t[2]*=e)}),t)}function Wi(t,e){if(void 0!==e&&null!==e||(e="centroid"),Array.isArray(e)||"object"==typeof e)return K(e);var n=t.bbox?t.bbox:H(t),o=n[0],i=n[1],a=n[2],s=n[3];switch(e){case"sw":case"southwest":case"westsouth":case"bottomleft":return r([o,i]);case"se":case"southeast":case"eastsouth":case"bottomright":return r([a,i]);case"nw":case"northwest":case"westnorth":case"topleft":return r([o,s]);case"ne":case"northeast":case"eastnorth":case"topright":return r([a,s]);case"center":return _n(t);case void 0:case null:case"centroid":return bn(t);default:throw new Error("invalid origin")}}function $i(t,e,n,r){if(r=r||{},!C(r))throw new Error("options is invalid");var o=r.units,i=r.zTranslation,a=r.mutate;if(!t)throw new Error("geojson is required");if(void 0===e||null===e||isNaN(e))throw new Error("distance is required");if(i&&"number"!=typeof i&&isNaN(i))throw new Error("zTranslation is not a number");if(i=void 0!==i?i:0,0===e&&0===i)return t;if(void 0===n||null===n||isNaN(n))throw new Error("direction is required");return e<0&&(e=-e,n=-n),!1!==a&&void 0!==a||(t=ve(t)),T(t,function(t){var r=J(fi(t,e,n,{units:o}));t[0]=r[0],t[1]=r[1],i&&3===t.length&&(t[2]+=i)}),t}function Hi(t){var e=t[0],n=t[1];return[n[0]-e[0],n[1]-e[1]]}function Ki(t,e){return t[0]*e[1]-e[0]*t[1]}function Ji(t,e){return[t[0]+e[0],t[1]+e[1]]}function Zi(t,e){return[t[0]-e[0],t[1]-e[1]]}function Qi(t,e){return[t*e[0],t*e[1]]}function ta(t,e){var n=t[0],r=Hi(t),o=e[0],i=Hi(e),a=Ki(r,i);return Ji(n,Qi(Ki(Zi(o,n),i)/a,r))}function ea(t,e){return 0===Ki(Hi(t),Hi(e))}function na(t,e){return!ea(t,e)&&ta(t,e)}function ra(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.units;if(!t)throw new Error("geojson is required");if(void 0===e||null===e||isNaN(e))throw new Error("distance is required");var o=ot(t),i=t.properties;switch(o){case"LineString":return oa(t,e,r);case"MultiLineString":var a=[];return z(t,function(t){a.push(oa(t,e,r).geometry.coordinates)}),l(a,i);default:throw new Error("geometry "+o+" is not supported")}}function oa(t,e,n){var r=[],o=y(e,n),i=J(t),a=[];return i.forEach(function(t,e){if(e!==i.length-1){var n=ia(t,i[e+1],o);if(r.push(n),e>0){var s=r[e-1],c=na(n,s);!1!==c&&(s[1]=c,n[0]=c),a.push(s[0]),e===i.length-2&&(a.push(n[0]),a.push(n[1]))}2===i.length&&(a.push(n[0]),a.push(n[1]))}}),s(a,t.properties)}function ia(t,e,n){var r=Math.sqrt((t[0]-e[0])*(t[0]-e[0])+(t[1]-e[1])*(t[1]-e[1])),o=t[0]+n*(e[1]-t[1])/r,i=e[0]+n*(e[1]-t[1])/r;return[[o,t[1]+n*(t[0]-e[0])/r],[i,e[1]+n*(t[0]-e[0])/r]]}function aa(t,e,n){var r=e[0]-t[0],o=e[1]-t[1],i=n[0]-e[0],a=n[1]-e[1];return Math.sign(r*a-i*o)}function sa(t,e){var n=t.geometry.coordinates.map(function(t){return t[0]}),r=t.geometry.coordinates.map(function(t){return t[1]}),o=e.geometry.coordinates.map(function(t){return t[0]}),i=e.geometry.coordinates.map(function(t){return t[1]});return Math.max(null,n)===Math.max(null,o)&&Math.max(null,r)===Math.max(null,i)&&Math.min(null,n)===Math.min(null,o)&&Math.min(null,r)===Math.min(null,i)}function ca(t,e){return e.geometry.coordinates[0].every(function(e){return ce(r(e),t)})}function ua(t,e){return t[0]===e[0]&&t[1]===e[1]}function la(t){if(!t)throw new Error("No geojson passed");if("FeatureCollection"!==t.type&&"GeometryCollection"!==t.type&&"MultiLineString"!==t.type&&"LineString"!==t.type&&"Feature"!==t.type)throw new Error("Invalid input type '"+t.type+"'. Geojson must be FeatureCollection, GeometryCollection, LineString, MultiLineString or Feature")}function da(t){var e=Sp.fromGeoJson(t);e.deleteDangles(),e.deleteCutEdges();var n=[],r=[];return e.getEdgeRings().filter(function(t){return t.isValid()}).forEach(function(t){t.isHole()?n.push(t):r.push(t)}),n.forEach(function(t){Ep.findEdgeRingContaining(t,r)&&r.push(t)}),u(r.map(function(t){return t.toPolygon()}))}function pa(t,e){var n;return z(t,function(t){z(e,function(e){if(!1===n)return!1;n=fa(t.geometry,e.geometry)})}),n}function fa(t,e){switch(t.type){case"Point":switch(e.type){case"Point":return!_a(t.coordinates,e.coordinates);case"LineString":return!ha(e,t);case"Polygon":return!ce(t,e)}break;case"LineString":switch(e.type){case"Point":return!ha(t,e);case"LineString":return!ga(t,e);case"Polygon":return!ma(e,t)}break;case"Polygon":switch(e.type){case"Point":return!ce(e,t);case"LineString":return!ma(t,e);case"Polygon":return!ya(e,t)}}}function ha(t,e){for(var n=0;n<t.coordinates.length-1;n++)if(va(t.coordinates[n],t.coordinates[n+1],e.coordinates))return!0;return!1}function ga(t,e){return Nr(t,e).features.length>0}function ma(t,e){return Nr(e,Yo(t)).features.length>0}function ya(t,e){for(var n=0;n<t.coordinates[0].length;n++)if(ce(t.coordinates[0][n],e))return!0;for(var r=0;r<e.coordinates[0].length;r++)if(ce(e.coordinates[0][r],t))return!0;return!1}function va(t,e,n){var r=n[0]-t[0],o=n[1]-t[1],i=e[0]-t[0],a=e[1]-t[1];return 0==r*a-o*i&&(Math.abs(i)>=Math.abs(a)?i>0?t[0]<=n[0]&&n[0]<=e[0]:e[0]<=n[0]&&n[0]<=t[0]:a>0?t[1]<=n[1]&&n[1]<=e[1]:e[1]<=n[1]&&n[1]<=t[1])}function _a(t,e){return t[0]===e[0]&&t[1]===e[1]}function ba(t,e){var n=ot(t),r=ot(e),o=nt(t),i=nt(e),a=J(t),s=J(e);switch(n){case"Point":switch(r){case"Point":return Na(a,s);default:throw new Error("feature2 "+r+" geometry not supported")}case"MultiPoint":switch(r){case"Point":return wa(o,i);case"MultiPoint":return xa(o,i);default:throw new Error("feature2 "+r+" geometry not supported")}case"LineString":switch(r){case"Point":return lo(i,o,{ignoreEndVertices:!0});case"LineString":return Sa(o,i);case"MultiPoint":return Ea(o,i);default:throw new Error("feature2 "+r+" geometry not supported")}case"Polygon":switch(r){case"Point":return ce(i,o,{ignoreBoundary:!0});case"LineString":return Ia(o,i);case"Polygon":return Pa(o,i);case"MultiPoint":return Ca(o,i);default:throw new Error("feature2 "+r+" geometry not supported")}default:throw new Error("feature1 "+n+" geometry not supported")}}function wa(t,e){var n,r=!1;for(n=0;n<t.coordinates.length;n++)if(Na(t.coordinates[n],e.coordinates)){r=!0;break}return r}function xa(t,e){for(var n=0;n<e.coordinates.length;n++){for(var r=!1,o=0;o<t.coordinates.length;o++)if(Na(e.coordinates[n],t.coordinates[o])){r=!0;break}if(!r)return!1}return!0}function Ea(t,e){for(var n=!1,r=0;r<e.coordinates.length;r++)if(lo(e.coordinates[r],t,{ignoreEndVertices:!0})&&(n=!0),!lo(e.coordinates[r],t))return!1;return!!n}function Ca(t,e){for(var n=0;n<e.coordinates.length;n++)if(!ce(e.coordinates[n],t,{ignoreBoundary:!0}))return!1;return!0}function Sa(t,e){for(var n=!1,r=0;r<e.coordinates.length;r++)if(lo({type:"Point",coordinates:e.coordinates[r]},t,{ignoreEndVertices:!0})&&(n=!0),!lo({type:"Point",coordinates:e.coordinates[r]},t,{ignoreEndVertices:!1}))return!1;return n}function Ia(t,e){var n=!1,r=0;if(!Aa(H(t),H(e)))return!1;for(r;r<e.coordinates.length-1;r++){if(ce({type:"Point",coordinates:Ma(e.coordinates[r],e.coordinates[r+1])},t,{ignoreBoundary:!0})){n=!0;break}}return n}function Pa(t,e){if(!Aa(H(t),H(e)))return!1;for(var n=0;n<e.coordinates[0].length;n++)if(!ce(e.coordinates[0][n],t))return!1;return!0}function Aa(t,e){return!(t[0]>e[0])&&(!(t[2]<e[2])&&(!(t[1]>e[1])&&!(t[3]<e[3])))}function Na(t,e){return t[0]===e[0]&&t[1]===e[1]}function Ma(t,e){return[(t[0]+e[0])/2,(t[1]+e[1])/2]}function La(t,e){var n=ot(t),r=ot(e),o=nt(t),i=nt(e);switch(n){case"MultiPoint":switch(r){case"LineString":return Da(o,i);case"Polygon":return Ra(o,i);default:throw new Error("feature2 "+r+" geometry not supported")}case"LineString":switch(r){case"MultiPoint":return Da(i,o);case"LineString":return Oa(o,i);case"Polygon":return Ta(o,i);default:throw new Error("feature2 "+r+" geometry not supported")}case"Polygon":switch(r){case"MultiPoint":return Ra(i,o);case"LineString":return Ta(i,o);default:throw new Error("feature2 "+r+" geometry not supported")}default:throw new Error("feature1 "+n+" geometry not supported")}}function Da(t,e){for(var n=!1,r=!1,o=t.coordinates.length,i=0;i<o&&!n&&!r;){for(var a=0;a<e.coordinates.length-1;a++){var s=!0;0!==a&&a!==e.coordinates.length-2||(s=!1),Fa(e.coordinates[a],e.coordinates[a+1],t.coordinates[i],s)?n=!0:r=!0}i++}return n&&r}function Oa(t,e){if(Nr(t,e).features.length>0)for(var n=0;n<t.coordinates.length-1;n++)for(var r=0;r<e.coordinates.length-1;r++){var o=!0;if(0!==r&&r!==e.coordinates.length-2||(o=!1),Fa(t.coordinates[n],t.coordinates[n+1],e.coordinates[r],o))return!0}return!1}function Ta(t,e){return Nr(t,Yo(e)).features.length>0}function Ra(t,e){for(var n=!1,o=!1,i=t.coordinates[0].length,a=0;a<i&&n&&o;)ce(r(t.coordinates[0][a]),e)?n=!0:o=!0,a++;return o&&o}function Fa(t,e,n,r){var o=n[0]-t[0],i=n[1]-t[1],a=e[0]-t[0],s=e[1]-t[1];return 0==o*s-i*a&&(r?Math.abs(a)>=Math.abs(s)?a>0?t[0]<=n[0]&&n[0]<=e[0]:e[0]<=n[0]&&n[0]<=t[0]:s>0?t[1]<=n[1]&&n[1]<=e[1]:e[1]<=n[1]&&n[1]<=t[1]:Math.abs(a)>=Math.abs(s)?a>0?t[0]<n[0]&&n[0]<e[0]:e[0]<n[0]&&n[0]<t[0]:s>0?t[1]<n[1]&&n[1]<e[1]:e[1]<n[1]&&n[1]<t[1])}function Ga(t){return t.coordinates.map(function(e){return{type:t.type.replace("Multi",""),coordinates:e}})}function qa(t,e){return t.hasOwnProperty("coordinates")?t.coordinates.length===e.coordinates.length:t.length===e.length}function ka(t,e){return Ap(t,e,{strict:!0})}function Ua(t,e){if(!t)throw new Error("feature1 is required");if(!e)throw new Error("feature2 is required");var n=ot(t);if(n!==ot(e))throw new Error("features must be of the same type");if("Point"===n)throw new Error("Point geometry not supported");if(new Mp({precision:6}).compare(t,e))return!1;var r=0;switch(n){case"MultiPoint":var o=U(t),i=U(e);o.forEach(function(t){i.forEach(function(e){t[0]===e[0]&&t[1]===e[1]&&r++})});break;case"LineString":case"MultiLineString":Y(t,function(t){Y(e,function(e){ui(t,e).features.length&&r++})});break;case"Polygon":case"MultiPolygon":Y(t,function(t){Y(e,function(e){Nr(t,e).features.length&&r++})})}return r>0}function Ba(t,e){if(!t)throw new Error("feature1 is required");if(!e)throw new Error("feature2 is required");return ot(t)===ot(e)&&new Mp({precision:6}).compare(Ye(t),Ye(e))}function Va(t,e,n){if("object"!=typeof(n=n||{}))throw new Error("options is invalid");var r=n.minPoints,o=n.units;if(et(t,"Point","Input must contain Points"),null===e||void 0===e)throw new Error("maxDistance is required");if(!(Math.sign(e)>0))throw new Error("Invalid maxDistance");if(!(void 0===r||null===r||Math.sign(r)>0))throw new Error("Invalid minPoints");t=ve(t,!0),r=r||3;var i=new Rp.DBSCAN,a=i.run(U(t),w(e,o),r,ye),s=-1;return a.forEach(function(e){s++,e.forEach(function(e){var n=t.features[e];n.properties||(n.properties={}),n.properties.cluster=s,n.properties.dbscan="core"})}),i.noise.forEach(function(e){var n=t.features[e];n.properties||(n.properties={}),n.properties.cluster?n.properties.dbscan="edge":n.properties.dbscan="noise"}),t}function za(t,e,n){n=n||[];for(var r=0;r<t;r++)n[r]=e;return n}function ja(t,e,n,r){var o=[],i=[],a=[],s=[],c=!1,u=r||zp,l=t.length,d=t[0].length,p=d>0,f=[];if(n)o="kmrand"==n?Bp(t,e):"kmpp"==n?Vp(t,e):n;else for(var h={};o.length<e;){var g=Math.floor(Math.random()*l);h[g]||(h[g]=!0,o.push(t[g]))}do{za(e,0,f);for(var m=0;m<l;m++){for(var y=1/0,v=0,_=0;_<e;_++){var s=p?Up(t[m],o[_]):Math.abs(t[m]-o[_]);s<=y&&(y=s,v=_)}a[m]=v,f[v]++}for(var b=[],i=[],w=0;w<e;w++)b[w]=p?za(d,0,b[w]):0,i[w]=o[w];if(p){for(var x=0;x<e;x++)o[x]=[];for(var E=0;E<l;E++)for(var C=a[E],S=b[C],I=t[E],P=0;P<d;P++)S[P]+=I[P];c=!0;for(var A=0;A<e;A++){for(var N=o[A],M=b[A],L=i[A],D=f[A],O=0;O<d;O++)N[O]=M[O]/D||0;if(c)for(var T=0;T<d;T++)if(L[T]!=N[T]){c=!1;break}}}else{for(var R=0;R<l;R++){b[a[R]]+=t[R]}for(var F=0;F<e;F++)o[F]=b[F]/f[F]||0;c=!0;for(var G=0;G<e;G++)if(i[G]!=o[G]){c=!1;break}}c=c||--u<=0}while(!c);return{it:zp-u,k:e,idxs:a,centroids:o}}function Ya(t,e){if("object"!=typeof(e=e||{}))throw new Error("options is invalid");var n=e.numberOfClusters,r=e.mutate;et(t,"Point","Input must contain Points");var o=t.features.length;n=n||Math.round(Math.sqrt(o/2)),n>o&&(n=o),!1!==r&&void 0!==r||(t=ve(t,!0));var i=U(t),a=i.slice(0,n),s=jp(i,n,a),c={};return s.centroids.forEach(function(t,e){c[e]=t}),q(t,function(t,e){var n=s.idxs[e];t.properties.cluster=n,t.properties.centroid=c[n]}),t}function Xa(t,e){if(!t)throw new Error("line1 is required");if(!e)throw new Error("line2 is required");if("LineString"!==$a(t,"line1"))throw new Error("line1 must be a LineString");if("LineString"!==$a(e,"line2"))throw new Error("line2 must be a LineString");for(var n=Sr(Ye(t)).features,r=Sr(Ye(e)).features,o=0;o<n.length;o++){var i=n[o].geometry.coordinates;if(!r[o])break;if(!Wa(i,r[o].geometry.coordinates))return!1}return!0}function Wa(t,e){return v(Dr(t[0],t[1]))===v(Dr(e[0],e[1]))}function $a(t,e){if(t.geometry&&t.geometry.type)return t.geometry.type;if(t.type)return t.type;throw new Error("Invalid GeoJSON object for "+e)}
// Freely distributable under the MIT License.
// Implements the astar search algorithm in javascript using a Binary Heap.
// Includes Binary Heap (with modifications) from Marijn Haverbeke.
// http://eloquentjavascript.net/appendix2.html
function Ha(t){for(var e=t,n=[];e.parent;)n.unshift(e),e=e.parent;return n}function Ka(){return new Qa(function(t){return t.f})}function Ja(t,e){e=e||{},this.nodes=[],this.diagonal=!!e.diagonal,this.grid=[];for(var n=0;n<t.length;n++){this.grid[n]=[];for(var r=0,o=t[n];r<o.length;r++){var i=new Za(n,r,o[r]);this.grid[n][r]=i,this.nodes.push(i)}}this.init()}function Za(t,e,n){this.x=t,this.y=e,this.weight=n}function Qa(t){this.content=[],this.scoreFunction=t}function ts(t,n,o){if(o=o||{},!C(o))throw new Error("options is invalid");var i=o.resolution,a=o.minDistance,c=o.obstacles||u([]);if(!t)throw new Error("start is required");if(!n)throw new Error("end is required");if(i&&!E(i)||i<=0)throw new Error("options.resolution must be a number, greater than 0");if(a)throw new Error("options.minDistance is not yet implemented");var l=K(t),d=K(n);switch(t=r(l),n=r(d),ot(c)){case"FeatureCollection":if(0===c.features.length)return s([l,d]);break;case"Polygon":c=u([e(nt(c))]);break;default:throw new Error("invalid obstacles")}var p=c;p.features.push(t),p.features.push(n);var f=H(Yi(dn(H(p)),1.15));if(!i){i=ye([f[0],f[1]],[f[2],f[1]],o)/100}p.features.pop(),p.features.pop();for(var h=f[0],g=f[1],m=f[2],y=f[3],v=i/ye([h,g],[m,g],o),_=v*(m-h),b=i/ye([h,g],[h,y],o),w=b*(y-g),x=m-h,S=y-g,I=Math.floor(x/_),P=Math.floor(S/w),A=(x-I*_)/2,N=(S-P*w)/2,M=[],L=[],D=[],O=[],T=1/0,R=1/0,F=y-N,G=0;F>=g;){for(var q=[],k=[],U=h+A,B=0;U<=m;){var V=r([U,F]),z=es(V,c);q.push(z?0:1),k.push(U+"|"+F);var j=ye(V,t);!z&&j<T&&(T=j,D={x:B,y:G});var Y=ye(V,n);!z&&Y<R&&(R=Y,O={x:B,y:G}),U+=_,B++}L.push(q),M.push(k),F-=w,G++}var X=new Ja(L,{diagonal:!0}),W=X.grid[D.y][D.x],$=X.grid[O.y][O.x],J=Yp.search(X,W,$),Z=[l];return J.forEach(function(t){var e=M[t.x][t.y].split("|");Z.push([+e[0],+e[1]])}),Z.push(d),Ye(s(Z))}function es(t,e){for(var n=0;n<e.features.length;n++)if(ce(t,e.features[n]))return!0;return!1}function ns(t){return t[0]}function rs(t){return t[1]}function os(){this._=null}function is(t){t.U=t.C=t.L=t.R=t.P=t.N=null}function as(t,e){var n=e,r=e.R,o=n.U;o?o.L===n?o.L=r:o.R=r:t._=r,r.U=o,n.U=r,n.R=r.L,n.R&&(n.R.U=n),r.L=n}function ss(t,e){var n=e,r=e.L,o=n.U;o?o.L===n?o.L=r:o.R=r:t._=r,r.U=o,n.U=r,n.L=r.R,n.L&&(n.L.U=n),r.R=n}function cs(t){for(;t.L;)t=t.L;return t}function us(t,e,n,r){var o=[null,null],i=Jp.push(o)-1;return o.left=t,o.right=e,n&&ds(o,t,e,n),r&&ds(o,e,t,r),Hp[t.index].halfedges.push(i),Hp[e.index].halfedges.push(i),o}function ls(t,e,n){var r=[e,n];return r.left=t,r}function ds(t,e,n,r){t[0]||t[1]?t.left===n?t[1]=r:t[0]=r:(t[0]=r,t.left=e,t.right=n)}function ps(t,e,n,r,o){var i,a=t[0],s=t[1],c=a[0],u=a[1],l=s[0],d=s[1],p=0,f=1,h=l-c,g=d-u;if(i=e-c,h||!(i>0)){if(i/=h,h<0){if(i<p)return;i<f&&(f=i)}else if(h>0){if(i>f)return;i>p&&(p=i)}if(i=r-c,h||!(i<0)){if(i/=h,h<0){if(i>f)return;i>p&&(p=i)}else if(h>0){if(i<p)return;i<f&&(f=i)}if(i=n-u,g||!(i>0)){if(i/=g,g<0){if(i<p)return;i<f&&(f=i)}else if(g>0){if(i>f)return;i>p&&(p=i)}if(i=o-u,g||!(i<0)){if(i/=g,g<0){if(i>f)return;i>p&&(p=i)}else if(g>0){if(i<p)return;i<f&&(f=i)}return!(p>0||f<1)||(p>0&&(t[0]=[c+p*h,u+p*g]),f<1&&(t[1]=[c+f*h,u+f*g]),!0)}}}}}function fs(t,e,n,r,o){var i=t[1];if(i)return!0;var a,s,c=t[0],u=t.left,l=t.right,d=u[0],p=u[1],f=l[0],h=l[1],g=(d+f)/2,m=(p+h)/2;if(h===p){if(g<e||g>=r)return;if(d>f){if(c){if(c[1]>=o)return}else c=[g,n];i=[g,o]}else{if(c){if(c[1]<n)return}else c=[g,o];i=[g,n]}}else if(a=(d-f)/(h-p),s=m-a*g,a<-1||a>1)if(d>f){if(c){if(c[1]>=o)return}else c=[(n-s)/a,n];i=[(o-s)/a,o]}else{if(c){if(c[1]<n)return}else c=[(o-s)/a,o];i=[(n-s)/a,n]}else if(p<h){if(c){if(c[0]>=r)return}else c=[e,a*e+s];i=[r,a*r+s]}else{if(c){if(c[0]<e)return}else c=[r,a*r+s];i=[e,a*e+s]}return t[0]=c,t[1]=i,!0}function hs(t,e,n,r){for(var o,i=Jp.length;i--;)fs(o=Jp[i],t,e,n,r)&&ps(o,t,e,n,r)&&(Math.abs(o[0][0]-o[1][0])>tf||Math.abs(o[0][1]-o[1][1])>tf)||delete Jp[i]}function gs(t){return Hp[t.index]={site:t,halfedges:[]}}function ms(t,e){var n=t.site,r=e.left,o=e.right;return n===o&&(o=r,r=n),o?Math.atan2(o[1]-r[1],o[0]-r[0]):(n===r?(r=e[1],o=e[0]):(r=e[0],o=e[1]),Math.atan2(r[0]-o[0],o[1]-r[1]))}function ys(t,e){return e[+(e.left!==t.site)]}function vs(t,e){return e[+(e.left===t.site)]}function _s(){for(var t,e,n,r,o=0,i=Hp.length;o<i;++o)if((t=Hp[o])&&(r=(e=t.halfedges).length)){var a=new Array(r),s=new Array(r);for(n=0;n<r;++n)a[n]=n,s[n]=ms(t,Jp[e[n]]);for(a.sort(function(t,e){return s[e]-s[t]}),n=0;n<r;++n)s[n]=e[a[n]];for(n=0;n<r;++n)e[n]=s[n]}}function bs(t,e,n,r){var o,i,a,s,c,u,l,d,p,f,h,g,m=Hp.length,y=!0;for(o=0;o<m;++o)if(i=Hp[o]){for(a=i.site,c=i.halfedges,s=c.length;s--;)Jp[c[s]]||c.splice(s,1);for(s=0,u=c.length;s<u;)f=vs(i,Jp[c[s]]),h=f[0],g=f[1],l=ys(i,Jp[c[++s%u]]),d=l[0],p=l[1],(Math.abs(h-d)>tf||Math.abs(g-p)>tf)&&(c.splice(s,0,Jp.push(ls(a,f,Math.abs(h-t)<tf&&r-g>tf?[t,Math.abs(d-t)<tf?p:r]:Math.abs(g-r)<tf&&n-h>tf?[Math.abs(p-r)<tf?d:n,r]:Math.abs(h-n)<tf&&g-e>tf?[n,Math.abs(d-n)<tf?p:e]:Math.abs(g-e)<tf&&h-t>tf?[Math.abs(p-e)<tf?d:t,e]:null))-1),++u);u&&(y=!1)}if(y){var v,_,b,w=1/0;for(o=0,y=null;o<m;++o)(i=Hp[o])&&(a=i.site,v=a[0]-t,_=a[1]-e,(b=v*v+_*_)<w&&(w=b,y=i));if(y){var x=[t,e],E=[t,r],C=[n,r],S=[n,e];y.halfedges.push(Jp.push(ls(a=y.site,x,E))-1,Jp.push(ls(a,E,C))-1,Jp.push(ls(a,C,S))-1,Jp.push(ls(a,S,x))-1)}}for(o=0;o<m;++o)(i=Hp[o])&&(i.halfedges.length||delete Hp[o])}function ws(){is(this),this.x=this.y=this.arc=this.site=this.cy=null}function xs(t){var e=t.P,n=t.N;if(e&&n){var r=e.site,o=t.site,i=n.site;if(r!==i){var a=o[0],s=o[1],c=r[0]-a,u=r[1]-s,l=i[0]-a,d=i[1]-s,p=2*(c*d-u*l);if(!(p>=-ef)){var f=c*c+u*u,h=l*l+d*d,g=(d*f-u*h)/p,m=(c*h-l*f)/p,y=Zp.pop()||new ws;y.arc=t,y.site=o,y.x=g+a,y.y=(y.cy=m+s)+Math.sqrt(g*g+m*m),t.circle=y;for(var v=null,_=Kp._;_;)if(y.y<_.y||y.y===_.y&&y.x<=_.x){if(!_.L){v=_.P;break}_=_.L}else{if(!_.R){v=_;break}_=_.R}Kp.insert(v,y),v||(Wp=y)}}}}function Es(t){var e=t.circle;e&&(e.P||(Wp=e.N),Kp.remove(e),Zp.push(e),is(e),t.circle=null)}function Cs(){is(this),this.edge=this.site=this.circle=null}function Ss(t){var e=Qp.pop()||new Cs;return e.site=t,e}function Is(t){Es(t),$p.remove(t),Qp.push(t),is(t)}function Ps(t){var e=t.circle,n=e.x,r=e.cy,o=[n,r],i=t.P,a=t.N,s=[t];Is(t);for(var c=i;c.circle&&Math.abs(n-c.circle.x)<tf&&Math.abs(r-c.circle.cy)<tf;)i=c.P,s.unshift(c),Is(c),c=i;s.unshift(c),Es(c);for(var u=a;u.circle&&Math.abs(n-u.circle.x)<tf&&Math.abs(r-u.circle.cy)<tf;)a=u.N,s.push(u),Is(u),u=a;s.push(u),Es(u);var l,d=s.length;for(l=1;l<d;++l)u=s[l],c=s[l-1],ds(u.edge,c.site,u.site,o);c=s[0],u=s[d-1],u.edge=us(c.site,u.site,null,o),xs(c),xs(u)}function As(t){for(var e,n,r,o,i=t[0],a=t[1],s=$p._;s;)if((r=Ns(s,a)-i)>tf)s=s.L;else{if(!((o=i-Ms(s,a))>tf)){r>-tf?(e=s.P,n=s):o>-tf?(e=s,n=s.N):e=n=s;break}if(!s.R){e=s;break}s=s.R}gs(t);var c=Ss(t);if($p.insert(e,c),e||n){if(e===n)return Es(e),n=Ss(e.site),$p.insert(c,n),c.edge=n.edge=us(e.site,c.site),xs(e),void xs(n);if(!n)return void(c.edge=us(e.site,c.site));Es(e),Es(n);var u=e.site,l=u[0],d=u[1],p=t[0]-l,f=t[1]-d,h=n.site,g=h[0]-l,m=h[1]-d,y=2*(p*m-f*g),v=p*p+f*f,_=g*g+m*m,b=[(m*v-f*_)/y+l,(p*_-g*v)/y+d];ds(n.edge,u,h,b),c.edge=us(u,t,null,b),n.edge=us(t,h,null,b),xs(e),xs(n)}}function Ns(t,e){var n=t.site,r=n[0],o=n[1],i=o-e;if(!i)return r;var a=t.P;if(!a)return-1/0;n=a.site;var s=n[0],c=n[1],u=c-e;if(!u)return s;var l=s-r,d=1/i-1/u,p=l/u;return d?(-p+Math.sqrt(p*p-2*d*(l*l/(-2*u)-c+u/2+o-i/2)))/d+r:(r+s)/2}function Ms(t,e){var n=t.N;if(n)return Ns(n,e);var r=t.site;return r[1]===e?r[0]:1/0}function Ls(t,e,n){return(t[0]-n[0])*(e[1]-t[1])-(t[0]-e[0])*(n[1]-t[1])}function Ds(t,e){return e[1]-t[1]||e[0]-t[0]}function Os(t,e){var n,r,o,i=t.sort(Ds).pop();for(Jp=[],Hp=new Array(t.length),$p=new os,Kp=new os;;)if(o=Wp,i&&(!o||i[1]<o.y||i[1]===o.y&&i[0]<o.x))i[0]===n&&i[1]===r||(As(i),n=i[0],r=i[1]),i=t.pop();else{if(!o)break;Ps(o.arc)}if(_s(),e){var a=+e[0][0],s=+e[0][1],c=+e[1][0],u=+e[1][1];hs(a,s,c,u),bs(a,s,c,u)}this.edges=Jp,this.cells=Hp,$p=Kp=Jp=Hp=null}function Ts(t){return t=t.slice(),t.push(t[0]),i([t])}function Rs(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.bbox||[-180,-85,180,85];if(!t)throw new Error("points is required");if(!Array.isArray(n))throw new Error("bbox is invalid");return et(t,"Point","points"),u(nf().x(function(t){return t.geometry.coordinates[0]}).y(function(t){return t.geometry.coordinates[1]}).extent([[n[0],n[1]],[n[2],n[3]]]).polygons(t.features).map(Ts))}function Fs(t,e,n,r){r=r||{};var o=r.steps||64,a=r.units||"kilometers",s=r.angle||0,c=r.pivot||t,u=r.properties||t.properties||{};if(!t)throw new Error("center is required");if(!e)throw new Error("xSemiAxis is required");if(!n)throw new Error("ySemiAxis is required");if(!C(r))throw new Error("options must be an object");if(!E(o))throw new Error("steps must be a number");if(!E(s))throw new Error("angle must be a number");var l=K(t);if("degrees"===a)var d=b(s);else e=fi(t,e,90,{units:a}),n=fi(t,n,0,{units:a}),e=K(e)[0]-l[0],n=K(n)[1]-l[1];for(var p=[],f=0;f<o;f+=1){var h=-360*f/o,g=e*n/Math.sqrt(Math.pow(n,2)+Math.pow(e,2)*Math.pow(Gs(h),2)),m=e*n/Math.sqrt(Math.pow(e,2)+Math.pow(n,2)/Math.pow(Gs(h),2));if(h<-90&&h>=-270&&(g=-g),h<-180&&h>=-360&&(m=-m),"degrees"===a){var y=g*Math.cos(d)+m*Math.sin(d),v=m*Math.cos(d)-g*Math.sin(d);g=y,m=v}p.push([g+l[0],m+l[1]])}return p.push(p[0]),"degrees"===a?i([p],u):ji(i([p],u),s,{pivot:c})}function Gs(t){var e=t*Math.PI/180;return Math.tan(e)}function qs(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.properties,o=e.weight;if(!t)throw new Error("geojson is required");var i=0,a=0,s=0;return B(t,function(t,e,n){var r=n[o];if(r=void 0===r||null===r?1:r,!E(r))throw new Error("weight value must be a number for feature index "+e);(r=Number(r))>0&&T(t,function(t){i+=t[0]*r,a+=t[1]*r,s+=r})}),r([i/s,a/s],n)}function ks(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.counter||10;if(!E(n))throw new Error("counter must be a number");var r=e.weight,o=qs(t,{weight:e.weight}),i=u([]);return q(t,function(t){i.features.push(bn(t,{weight:t.properties[r]}))}),i.properties={tolerance:e.tolerance,medianCandidates:[]},Us(o.geometry.coordinates,[0,0],i,n)}function Us(t,e,n,o){var i=n.properties.tolerance||.001,a=0,s=0,c=0,u=0;if(q(n,function(e){var n=e.properties.weight,r=void 0===n||null===n?1:n;if(r=Number(r),!E(r))throw new Error("weight value must be a number");if(r>0){u+=1;var o=r*ye(e,t);0===o&&(o=1);var i=r/o;a+=e.geometry.coordinates[0]*i,s+=e.geometry.coordinates[1]*i,c+=i}}),u<1)throw new Error("no features to measure");var l=a/c,d=s/c;return 1===u||0===o||Math.abs(l-e[0])<i&&Math.abs(d-e[1])<i?r([l,d],{medianCandidates:n.properties.medianCandidates}):(n.properties.medianCandidates.push([l,d]),Us([l,d],t,n,o-1))}function Bs(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.steps||64,r=e.weight,o=e.properties||{};if(!E(n))throw new Error("steps must be a number");if(!C(o))throw new Error("properties must be a number");var i=U(t).length,a=qs(t,{weight:r}),s=0,c=0,l=0;q(t,function(t){var e=t.properties[r]||1,n=Vs(J(t),J(a));s+=Math.pow(n.x,2)*e,c+=Math.pow(n.y,2)*e,l+=n.x*n.y*e});var d=s-c,p=Math.sqrt(Math.pow(d,2)+4*Math.pow(l,2)),f=2*l,h=Math.atan((d+p)/f),g=180*h/Math.PI,m=0,y=0,v=0;q(t,function(t){var e=t.properties[r]||1,n=Vs(J(t),J(a));m+=Math.pow(n.x*Math.cos(h)-n.y*Math.sin(h),2)*e,y+=Math.pow(n.x*Math.sin(h)+n.y*Math.cos(h),2)*e,v+=e});var _=Math.sqrt(2*m/v),b=Math.sqrt(2*y/v),w=Fs(a,_,b,{units:"degrees",angle:g,steps:n,properties:o}),x=de(t,u([w])),S={meanCenterCoordinates:J(a),semiMajorAxis:_,semiMinorAxis:b,numberOfFeatures:i,angle:g,percentageWithinEllipse:100*U(x).length/i};return w.properties.standardDeviationalEllipse=S,w}function Vs(t,e){return{x:t[0]-e[0],y:t[1]-e[1]}}function zs(t){if(C(t)&&(t=t.bbox),t&&!Array.isArray(t))throw new Error("bbox is invalid");return t?Js(t):[Hs(),Ks()]}function js(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.bbox;void 0!==t&&null!==t||(t=1);for(var o=[],i=0;i<t;i++)o.push(r(zs(n)));return u(o)}function Ys(t,e){function n(t,e,n){n[e]=e>0?t+n[e-1]:t}function r(t){t=2*t*Math.PI/p[p.length-1];var e=Math.random();d.push([e*s*Math.sin(t),e*s*Math.cos(t)])}if(e=e||{},!C(e))throw new Error("options is invalid");var o=e.bbox,a=e.num_vertices,s=e.max_radial_length;void 0!==t&&null!==t||(t=1),E(a)||(a=10),E(s)||(s=10);for(var c=[],l=0;l<t;l++){var d=[],p=Array.apply(null,new Array(a+1)).map(Math.random);p.forEach(n),p.forEach(r),d[d.length-1]=d[0],d=d.map(Ws(zs(o))),c.push(i([d]))}return u(c)}function Xs(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.bbox,r=e.num_vertices,o=e.max_length,i=e.max_rotation;void 0!==t&&null!==t||(t=1),(!E(r)||r<2)&&(r=10),E(o)||(o=1e-4),E(i)||(i=Math.PI/8);for(var a=[],c=0;c<t;c++){for(var l=zs(n),d=[l],p=0;p<r-1;p++){var f=0===p?2*Math.random()*Math.PI:Math.tan((d[p][1]-d[p-1][1])/(d[p][0]-d[p-1][0])),h=f+(Math.random()-.5)*i*2,g=Math.random()*o;d.push([d[p][0]+g*Math.cos(h),d[p][1]+g*Math.sin(h)])}a.push(s(d))}return u(a)}function Ws(t){return function(e){return[e[0]+t[0],e[1]+t[1]]}}function $s(){return Math.random()-.5}function Hs(){return 360*$s()}function Ks(){return 180*$s()}function Js(t){return[Math.random()*(t[2]-t[0])+t[0],Math.random()*(t[3]-t[1])+t[1]]}function Zs(t,e){if(!t)throw new Error("geojson is required");if("FeatureCollection"!==t.type)throw new Error("geojson must be a FeatureCollection");if(void 0===e||null===e)throw new Error("filter is required");var n=[];return q(t,function(t){nc(t.properties,e)&&n.push(t)}),u(n)}function Qs(t,e,n){if(!t)throw new Error("geojson is required");if("FeatureCollection"!==t.type)throw new Error("geojson must be a FeatureCollection");if(void 0===e||null===e)throw new Error("property is required");for(var r=ec(t,e),o=Object.keys(r),i=0;i<o.length;i++){for(var a=o[i],s=r[a],c=[],l=0;l<s.length;l++)c.push(t.features[s[l]]);n(u(c),a,i)}}function tc(t,e,n,r){var o=r;return Qs(t,e,function(t,e,i){o=0===i&&void 0===r?t:n(o,t,e,i)}),o}function ec(t,e){var n={};return q(t,function(t,r){var o=t.properties||{};if(o.hasOwnProperty(e)){var i=o[e];n.hasOwnProperty(i)?n[i].push(r):n[i]=[r]}}),n}function nc(t,e){if(void 0===t)return!1;var n=typeof e;if("number"===n||"string"===n)return t.hasOwnProperty(e);if(Array.isArray(e)){for(var r=0;r<e.length;r++)if(!nc(t,e[r]))return!1;return!0}return rc(t,e)}function rc(t,e){for(var n=Object.keys(e),r=0;r<n.length;r++){var o=n[r];if(t[o]!==e[o])return!1}return!0}function oc(t,e){if(!e)return{};if(!e.length)return{};for(var n={},r=0;r<e.length;r++){var o=e[r];t.hasOwnProperty(o)&&(n[o]=t[o])}return n}function ic(){}function ac(t){this.message=t||""}function sc(t){this.message=t||""}function cc(){}function uc(t){return null===t?Ch:t.color}function lc(t){return null===t?null:t.parent}function dc(t,e){null!==t&&(t.color=e)}function pc(t){return null===t?null:t.left}function fc(t){return null===t?null:t.right}function hc(){this.root_=null,this.size_=0}function gc(){}function mc(){this.array_=[],arguments[0]instanceof uh&&this.addAll(arguments[0])}function yc(){}function vc(){}function _c(){}function bc(){}function wc(){}function xc(t){this.message=t||""}function Ec(){this.array_=[]}function Cc(t,n){var r=nt(t),o=nt(n),i=t.properties||{};if(r=Sc(r),o=Sc(o),!r)return null;if(!o)return e(r,i);var a=new hg,s=a.read(r),c=a.read(o),u=mv.difference(s,c);return u.isEmpty()?null:e((new gg).write(u),i)}function Sc(t){switch(t.type){case"Polygon":return eo(t)>1?t:null;case"MultiPolygon":var e=[];if(z(t,function(t){eo(t)>1&&e.push(t.geometry.coordinates)}),e.length)return{type:"MultiPolygon",coordinates:e}}}function Ic(){this.reset()}function Pc(t,e,n){var r=t.s=e+n,o=r-e,i=r-o;t.t=e-i+(n-o)}function Ac(t){return t>1?0:t<-1?Nv:Math.acos(t)}function Nc(t){return t>1?Mv:t<-1?-Mv:Math.asin(t)}function Mc(){}function Lc(t,e){t&&Yv.hasOwnProperty(t.type)&&Yv[t.type](t,e)}function Dc(t,e,n){var r,o=-1,i=t.length-n;for(e.lineStart();++o<i;)r=t[o],e.point(r[0],r[1],r[2]);e.lineEnd()}function Oc(t,e){var n=-1,r=t.length;for(e.polygonStart();++n<r;)Dc(t[n],e,1);e.polygonEnd()}function Tc(t){return[Gv(t[1],t[0]),Nc(t[2])]}function Rc(t){var e=t[0],n=t[1],r=qv(n);return[r*qv(e),r*Bv(e),Bv(n)]}function Fc(t,e){return t[0]*e[0]+t[1]*e[1]+t[2]*e[2]}function Gc(t,e){return[t[1]*e[2]-t[2]*e[1],t[2]*e[0]-t[0]*e[2],t[0]*e[1]-t[1]*e[0]]}function qc(t,e){t[0]+=e[0],t[1]+=e[1],t[2]+=e[2]}function kc(t,e){return[t[0]*e,t[1]*e,t[2]*e]}function Uc(t){var e=Vv(t[0]*t[0]+t[1]*t[1]+t[2]*t[2]);t[0]/=e,t[1]/=e,t[2]/=e}function Bc(t,e){return[t>Nv?t-Dv:t<-Nv?t+Dv:t,e]}function Vc(t,e,n){return(t%=Dv)?e||n?Wv(jc(t),Yc(e,n)):jc(t):e||n?Yc(e,n):Bc}function zc(t){return function(e,n){return e+=t,[e>Nv?e-Dv:e<-Nv?e+Dv:e,n]}}function jc(t){var e=zc(t);return e.invert=zc(-t),e}function Yc(t,e){function n(t,e){var n=qv(e),s=qv(t)*n,c=Bv(t)*n,u=Bv(e),l=u*r+s*o;return[Gv(c*i-l*a,s*r-u*o),Nc(l*i+c*a)]}var r=qv(t),o=Bv(t),i=qv(e),a=Bv(e);return n.invert=function(t,e){var n=qv(e),s=qv(t)*n,c=Bv(t)*n,u=Bv(e),l=u*i-c*a;return[Gv(c*i+u*a,s*r+l*o),Nc(l*r-s*o)]},n}function Xc(t,e,n,r,o,i){if(n){var a=qv(e),s=Bv(e),c=r*n;null==o?(o=e+r*Dv,i=e-c/2):(o=Wc(a,o),i=Wc(a,i),(r>0?o<i:o>i)&&(o+=r*Dv));for(var u,l=o;r>0?l>i:l<i;l-=c)u=Tc([a,-s*qv(l),-s*Bv(l)]),t.point(u[0],u[1])}}function Wc(t,e){e=Rc(e),e[0]-=t,Uc(e);var n=Ac(-e[1]);return((-e[2]<0?-n:n)+Dv-Av)%Dv}function $c(t,e,n,r){this.x=t,this.z=e,this.o=n,this.e=r,this.v=!1,this.n=this.p=null}function Hc(t){if(e=t.length){for(var e,n,r=0,o=t[0];++r<e;)o.n=n=t[r],n.p=o,o=n;o.n=n=t[0],n.p=o}}function Kc(t){return function(e,n){return Qv(t(e),n)}}function Jc(t,e,n,r){function o(o,i){return t<=o&&o<=n&&e<=i&&i<=r}function i(o,i,s,u){var l=0,d=0;if(null==o||(l=a(o,s))!==(d=a(i,s))||c(o,i)<0^s>0)do{u.point(0===l||3===l?t:n,l>1?r:e)}while((l=(l+s+4)%4)!==d);else u.point(i[0],i[1])}function a(r,o){return Rv(r[0]-t)<Av?o>0?0:3:Rv(r[0]-n)<Av?o>0?2:1:Rv(r[1]-e)<Av?o>0?1:0:o>0?3:2}function s(t,e){return c(t.x,e.x)}function c(t,e){var n=a(t,1),r=a(e,1);return n!==r?n-r:0===n?e[1]-t[1]:1===n?t[0]-e[0]:2===n?t[1]-e[1]:e[0]-t[0]}return function(a){function c(t,e){o(t,e)&&I.point(t,e)}function u(){for(var e=0,n=0,o=m.length;n<o;++n)for(var i,a,s=m[n],c=1,u=s.length,l=s[0],d=l[0],p=l[1];c<u;++c)i=d,a=p,l=s[c],d=l[0],p=l[1],a<=r?p>r&&(d-i)*(r-a)>(p-a)*(t-i)&&++e:p<=r&&(d-i)*(r-a)<(p-a)*(t-i)&&--e;return e}function l(){I=P,g=[],m=[],S=!0}function d(){var t=u(),e=S&&t,n=(g=t_(g)).length;(e||n)&&(a.polygonStart(),e&&(a.lineStart(),i(null,null,1,a),a.lineEnd()),n&&Zv(g,s,t,i,a),a.polygonEnd()),I=a,g=m=y=null}function p(){A.point=h,m&&m.push(y=[]),C=!0,E=!1,w=x=NaN}function f(){g&&(h(v,_),b&&E&&P.rejoin(),g.push(P.result())),A.point=c,E&&I.lineEnd()}function h(i,a){var s=o(i,a);if(m&&y.push([i,a]),C)v=i,_=a,b=s,C=!1,s&&(I.lineStart(),I.point(i,a));else if(s&&E)I.point(i,a);else{var c=[w=Math.max(n_,Math.min(e_,w)),x=Math.max(n_,Math.min(e_,x))],u=[i=Math.max(n_,Math.min(e_,i)),a=Math.max(n_,Math.min(e_,a))];Kv(c,u,t,e,n,r)?(E||(I.lineStart(),I.point(c[0],c[1])),I.point(u[0],u[1]),s||I.lineEnd(),S=!1):s&&(I.lineStart(),I.point(i,a),S=!1)}w=i,x=a,E=s}var g,m,y,v,_,b,w,x,E,C,S,I=a,P=Hv(),A={point:c,lineStart:p,lineEnd:f,polygonStart:l,polygonEnd:d};return A}}function Zc(t,e){t<a_&&(a_=t),t>c_&&(c_=t),e<s_&&(s_=e),e>u_&&(u_=e)}function Qc(t){return t.length>1}function tu(t,e){return((t=t.x)[0]<0?t[1]-Mv-Av:Mv-t[1])-((e=e.x)[0]<0?e[1]-Mv-Av:Mv-e[1])}function eu(t){var e,n=NaN,r=NaN,o=NaN;return{lineStart:function(){t.lineStart(),e=1},point:function(i,a){var s=i>0?Nv:-Nv,c=Rv(i-n);Rv(c-Nv)<Av?(t.point(n,r=(r+a)/2>0?Mv:-Mv),t.point(o,r),t.lineEnd(),t.lineStart(),t.point(s,r),t.point(i,r),e=0):o!==s&&c>=Nv&&(Rv(n-o)<Av&&(n-=o*Av),Rv(i-s)<Av&&(i-=s*Av),r=nu(n,r,i,a),t.point(o,r),t.lineEnd(),t.lineStart(),t.point(s,r),e=0),t.point(n=i,r=a),o=s},lineEnd:function(){t.lineEnd(),n=r=NaN},clean:function(){return 2-e}}}function nu(t,e,n,r){var o,i,a=Bv(t-n);return Rv(a)>Av?Fv((Bv(e)*(i=qv(r))*Bv(n)-Bv(r)*(o=qv(e))*Bv(t))/(o*i*a)):(e+r)/2}function ru(t,e,n,r){var o;if(null==t)o=n*Mv,r.point(-Nv,o),r.point(0,o),r.point(Nv,o),r.point(Nv,0),r.point(Nv,-o),r.point(0,-o),r.point(-Nv,-o),r.point(-Nv,0),r.point(-Nv,o);else if(Rv(t[0]-e[0])>Av){var i=t[0]<e[0]?Nv:-Nv;o=n*i/2,r.point(-i,o),r.point(0,o),r.point(i,o)}else r.point(e[0],e[1])}function ou(t){return function(e){var n=new iu;for(var r in t)n[r]=t[r];return n.stream=e,n}}function iu(){}function au(t,e,n){var r=e[1][0]-e[0][0],o=e[1][1]-e[0][1],i=t.clipExtent&&t.clipExtent();t.scale(150).translate([0,0]),null!=i&&t.clipExtent(null),Xv(n,t.stream(l_));var a=l_.result(),s=Math.min(r/(a[1][0]-a[0][0]),o/(a[1][1]-a[0][1])),c=+e[0][0]+(r-s*(a[1][0]+a[0][0]))/2,u=+e[0][1]+(o-s*(a[1][1]+a[0][1]))/2;return null!=i&&t.clipExtent(i),t.scale(150*s).translate([c,u])}function su(t,e,n){return au(t,[[0,0],e],n)}function cu(t){return ou({point:function(e,n){e=t(e,n),this.stream.point(e[0],e[1])}})}function uu(t,e){function n(r,o,i,a,s,c,u,l,d,p,f,h,g,m){var y=u-r,v=l-o,_=y*y+v*v;if(_>4*e&&g--){var b=a+p,w=s+f,x=c+h,E=Vv(b*b+w*w+x*x),C=Nc(x/=E),S=Rv(Rv(x)-1)<Av||Rv(i-d)<Av?(i+d)/2:Gv(w,b),I=t(S,C),P=I[0],A=I[1],N=P-r,M=A-o,L=v*N-y*M;(L*L/_>e||Rv((y*N+v*M)/_-.5)>.3||a*p+s*f+c*h<g_)&&(n(r,o,i,a,s,c,P,A,S,b/=E,w/=E,x,g,m),m.point(P,A),n(P,A,S,b,w,x,u,l,d,p,f,h,g,m))}}return function(e){function r(n,r){n=t(n,r),e.point(n[0],n[1])}function o(){y=NaN,x.point=i,e.lineStart()}function i(r,o){var i=Rc([r,o]),a=t(r,o);n(y,v,m,_,b,w,y=a[0],v=a[1],m=r,_=i[0],b=i[1],w=i[2],h_,e),e.point(y,v)}function a(){x.point=r,e.lineEnd()}function s(){o(),x.point=c,x.lineEnd=u}function c(t,e){i(l=t,e),d=y,p=v,f=_,h=b,g=w,x.point=i}function u(){n(y,v,m,_,b,w,d,p,l,f,h,g,h_,e),x.lineEnd=a,a()}var l,d,p,f,h,g,m,y,v,_,b,w,x={point:r,lineStart:o,lineEnd:a,polygonStart:function(){e.polygonStart(),x.lineStart=s},polygonEnd:function(){e.polygonEnd(),x.lineStart=o}};return x}}function lu(t){return du(function(){return t})()}function du(t){function e(t){return t=l(t[0]*Tv,t[1]*Tv),[t[0]*m+s,c-t[1]*m]}function n(t){return(t=l.invert((t[0]-s)/m,(c-t[1])/m))&&[t[0]*Ov,t[1]*Ov]}function r(t,e){return t=a(t,e),[t[0]*m+s,c-t[1]*m]}function o(){l=Wv(u=Vc(w,x,E),a);var t=a(_,b);return s=y-t[0]*m,c=v+t[1]*m,i()}function i(){return h=g=null,e}var a,s,c,u,l,d,p,f,h,g,m=150,y=480,v=250,_=0,b=0,w=0,x=0,E=0,C=null,S=p_,I=null,P=i_,A=.5,N=m_(r,A);return e.stream=function(t){return h&&g===t?h:h=y_(S(u,N(P(g=t))))},e.clipAngle=function(t){return arguments.length?(S=+t?f_(C=t*Tv,6*Tv):(C=null,p_),i()):C*Ov},e.clipExtent=function(t){return arguments.length?(P=null==t?(I=d=p=f=null,i_):Jc(I=+t[0][0],d=+t[0][1],p=+t[1][0],f=+t[1][1]),i()):null==I?null:[[I,d],[p,f]]},e.scale=function(t){return arguments.length?(m=+t,o()):m},e.translate=function(t){return arguments.length?(y=+t[0],v=+t[1],o()):[y,v]},e.center=function(t){return arguments.length?(_=t[0]%360*Tv,b=t[1]%360*Tv,o()):[_*Ov,b*Ov]},e.rotate=function(t){return arguments.length?(w=t[0]%360*Tv,x=t[1]%360*Tv,E=t.length>2?t[2]%360*Tv:0,o()):[w*Ov,x*Ov,E*Ov]},e.precision=function(t){return arguments.length?(N=m_(r,A=t*t),i()):Vv(A)},e.fitExtent=function(t,n){return au(e,t,n)},e.fitSize=function(t,n){return su(e,t,n)},function(){return a=t.apply(this,arguments),e.invert=a.invert&&n,o()}}function pu(t,e){return[t,Uv(zv((Mv+e)/2))]}function fu(t){function e(){var e=Nv*s(),a=i($v(i.rotate()).invert([0,0]));return u(null==l?[[a[0]-e,a[1]-e],[a[0]+e,a[1]+e]]:t===pu?[[Math.max(a[0]-e,l),n],[Math.min(a[0]+e,r),o]]:[[l,Math.max(a[1]-e,n)],[r,Math.min(a[1]+e,o)]])}var n,r,o,i=lu(t),a=i.center,s=i.scale,c=i.translate,u=i.clipExtent,l=null;return i.scale=function(t){return arguments.length?(s(t),e()):s()},i.translate=function(t){return arguments.length?(c(t),e()):c()},i.center=function(t){return arguments.length?(a(t),e()):a()},i.clipExtent=function(t){return arguments.length?(null==t?l=n=r=o=null:(l=+t[0][0],n=+t[0][1],r=+t[1][0],o=+t[1][1]),e()):null==l?null:[[l,n],[r,o]]},e()}function hu(t,e){return[Uv(zv((Mv+e)/2)),-t]}function gu(t,e,n){n=n||{};var r=n.units,o=n.steps||64;if(!t)throw new Error("geojson is required");if("object"!=typeof n)throw new Error("options must be an object");if("number"!=typeof o)throw new Error("steps must be an number");if(void 0===e)throw new Error("radius is required");if(o<=0)throw new Error("steps must be greater than 0");o=o||64,r=r||"kilometers";var i=[];switch(t.type){case"GeometryCollection":return B(t,function(t){var n=mu(t,e,r,o);n&&i.push(n)}),u(i);case"FeatureCollection":return q(t,function(t){var n=mu(t,e,r,o);n&&q(n,function(t){t&&i.push(t)})}),u(i)}return mu(t,e,r,o)}function mu(t,n,r,o){var i=t.properties||{},a="Feature"===t.type?t.geometry:t;if("GeometryCollection"===a.type){var s=[];return B(t,function(t){var e=mu(t,n,r,o);e&&s.push(e)}),u(s)}var c,l=H(t),d=l[1]>50&&l[3]>50;c=d?{type:a.type,coordinates:vu(a.coordinates,bu(a))}:Fr(a);var p=new hg,f=p.read(c),h=g(m(n,r),"meters"),y=iy.bufferOp(f,h);if(y=(new gg).write(y),!yu(y.coordinates)){var v;return v=d?{type:y.type,coordinates:_u(y.coordinates,bu(a))}:Gr(y),v.geometry?v:e(v,i)}}function yu(t){return Array.isArray(t[0])?yu(t[0]):isNaN(t[0])}function vu(t,e){return"object"!=typeof t[0]?e(t):t.map(function(t){return vu(t,e)})}function _u(t,e){return"object"!=typeof t[0]?e.invert(t):t.map(function(t){return _u(t,e)})}function bu(t){var e=_n(t).geometry.coordinates.reverse(),n=e.map(function(t){return-t});return v_().center(e).rotate(n).scale(Uu)}function wu(){for(var t=new hg,e=t.read(JSON.stringify(arguments[0].geometry)),n=1;n<arguments.length;n++)e=Sv.union(e,t.read(JSON.stringify(arguments[n].geometry)));return e=(new gg).write(e),{type:"Feature",geometry:e,properties:arguments[0].properties}}function xu(t,n){var r=nt(t),o=nt(n);if(Ye(So(o,{precision:4})).coordinates[0].length<4)return null;if(Ye(So(r,{precision:4})).coordinates[0].length<4)return null;var i=new hg,a=i.read(So(r)),s=i.read(So(o)),c=mv.intersection(a,s);return c.isEmpty()?null:e((new gg).write(c))}/**
 * @license get-closest https://github.com/cosmosio/get-closest
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2014-2017 Olivier Scherrer <pode.fr@gmail.com>
 */
function Eu(t,e,n){var r,o;if(!Array.isArray(e))throw new Error("Get closest expects an array as second argument");return e.forEach(function(e,i){var a=n(e,t);a>=0&&(void 0===o||a<o)&&(o=a,r=i)}),r}function Cu(t,e){return Eu(t,e,function(t,e){return t-e})}function Su(t,e){if(e=e||{},!C(e))throw new Error("options is invalid");var n=e.propertyName;et(t,"Polygon","dissolve");var r=ve(t),o=r.features,i=[];o.forEach(function(t,e){t.properties.origIndexPosition=e});var a=xr();a.load(r);for(var s in o){var c=o[s],u=!1;if(a.search(c).features.forEach(function(t){c=o[s];var e=t.properties.origIndexPosition;if(i.length>0&&0!==e)if(e>i[i.length-1])e-=i.length;else{var r=Cu(e,i);0!==r&&(e-=r)}if(e!==+s){var l=o[e];l&&c&&(void 0!==n&&l.properties[n]!==c.properties[n]||Ua(c,l)&&Iu(c,l)&&(o[s]=wu(c,l),i.push(t.properties.origIndexPosition),i.sort(function(t,e){return t-e}),a.remove(t),o.splice(e,1),c.properties.origIndexPosition=s,a.remove(c,function(t,e){return t.properties.origIndexPosition===e.properties.origIndexPosition}),u=!0))}}),u){if(!c)continue;c.properties.origIndexPosition=s,a.insert(c),s--}}return o.forEach(function(t){delete t.properties.origIndexPosition,delete t.bbox}),r}function Iu(t,e){return Nr(s(U(t)),s(U(e))).features.length>0}function Pu(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.properties||{},o=n.triangles,i=n.mask;if(null===e||void 0===e)throw new Error("cellSide is required");if(!E(e))throw new Error("cellSide is invalid");if(!t)throw new Error("bbox is required");if(!Array.isArray(t))throw new Error("bbox must be array");if(4!==t.length)throw new Error("bbox must contain 4 numbers");if(i&&-1===["Polygon","MultiPolygon"].indexOf(ot(i)))throw new Error("options.mask must be a (Multi)Polygon");var a=t[0],s=t[1],c=t[2],l=t[3],d=(s+l)/2,p=(a+c)/2,f=2*e/ye([a,d],[c,d],n),h=f*(c-a),g=2*e/ye([p,s],[p,l],n),m=g*(l-s),y=h/2,v=2*y,_=Math.sqrt(3)/2*m,b=c-a,w=l-s,x=.75*v,S=_,I=(b-v)/(v-y/2),P=Math.floor(I),A=(P*x-y/2-b)/2-y/2+x/2,N=Math.floor((w-_)/_),M=(w-N*_)/2,L=N*_-w>_/2;L&&(M-=_/4);for(var D=[],O=[],T=0;T<6;T++){var R=2*Math.PI/6*T;D.push(Math.cos(R)),O.push(Math.sin(R))}for(var F=[],G=0;G<=P;G++)for(var q=0;q<=N;q++){var k=G%2==1;if((0!==q||!k)&&(0!==q||!L)){var U=G*x+a-A,B=q*S+s+M;if(k&&(B-=_/2),!0===o)Nu([U,B],h/2,m/2,r,D,O).forEach(function(t){i?xu(i,t)&&F.push(t):F.push(t)});else{var V=Au([U,B],h/2,m/2,r,D,O);i?xu(i,V)&&F.push(V):F.push(V)}}}return u(F)}function Au(t,e,n,r,o,a){for(var s=[],c=0;c<6;c++){var u=t[0]+e*o[c],l=t[1]+n*a[c];s.push([u,l])}return s.push(s[0].slice()),i([s],r)}function Nu(t,e,n,r,o,a){for(var s=[],c=0;c<6;c++){var u=[];u.push(t),u.push([t[0]+e*o[c],t[1]+n*a[c]]),u.push([t[0]+e*o[(c+1)%6],t[1]+n*a[(c+1)%6]]),u.push(t),s.push(i([u],r))}return s}function Mu(t,e){var n=Ou(e),r=Du(t),o=r[0],i=r[1];return o=Tu(o),i=Tu(i),Lu(n,o,i)}function Lu(t,e,n){var r=[];return r.push(t.geometry.coordinates[0]),z(e,function(t){r.push(t.geometry.coordinates[0])}),z(n,function(t){r.push(t.geometry.coordinates[0])}),i(r)}function Du(t){var e=[],n=[];return z(t,function(t){var r=t.geometry.coordinates,o=r[0],a=r.slice(1);e.push(i([o])),a.forEach(function(t){n.push(i([t]))})}),[u(e),u(n)]}function Ou(t){var e=[[[180,90],[-180,90],[-180,-90],[180,-90],[180,90]]];return i(t&&t.geometry.coordinates||e)}function Tu(t){if(t.features.length<=1)return t;var e=Fu(t),n=[],r={};return z(t,function(t,o){if(r[o])return!0;for(e.remove({index:o},Ru),r[o]=!0;;){var i=H(t),a=e.search({minX:i[0],minY:i[1],maxX:i[2],maxY:i[3]});if(a.length>0){var s=a.map(function(t){return r[t.index]=!0,e.remove({index:t.index},Ru),t.geojson});s.push(t),t=wu.apply(this,s)}if(0===a.length)break}n.push(t)}),u(n)}function Ru(t,e){return t.index===e.index}function Fu(t){var e=Ku(),n=[];return z(t,function(t,e){var r=H(t);n.push({minX:r[0],minY:r[1],maxX:r[2],maxY:r[3],geojson:t,index:e})}),e.load(n),e}function Gu(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.properties,o=n.mask,a=[];if(null===e||void 0===e)throw new Error("cellSide is required");if(!E(e))throw new Error("cellSide is invalid");if(!t)throw new Error("bbox is required");if(!Array.isArray(t))throw new Error("bbox must be array");if(4!==t.length)throw new Error("bbox must contain 4 numbers");if(o&&-1===["Polygon","MultiPolygon"].indexOf(ot(o)))throw new Error("options.mask must be a (Multi)Polygon");for(var s=t[0],c=t[1],l=t[2],d=t[3],p=e/ye([s,c],[l,c],n),f=p*(l-s),h=e/ye([s,c],[s,d],n),g=h*(d-c),m=l-s,y=d-c,v=Math.floor(m/f),_=Math.floor(y/g),b=(m-v*f)/2,w=(y-_*g)/2,x=s+b,S=0;S<v;S++){for(var I=c+w,P=0;P<_;P++){var A=i([[[x,I],[x,I+g],[x+f,I+g],[x+f,I],[x,I]]],r);o?xu(o,A)&&a.push(A):a.push(A),I+=g}x+=f}return u(a)}function qu(t,e,n){if(n=n||{},!C(n))throw new Error("options is invalid");var r=n.properties,o=n.mask,a=[];if(null===e||void 0===e)throw new Error("cellSide is required");if(!E(e))throw new Error("cellSide is invalid");if(!t)throw new Error("bbox is required");if(!Array.isArray(t))throw new Error("bbox must be array");if(4!==t.length)throw new Error("bbox must contain 4 numbers");if(o&&-1===["Polygon","MultiPolygon"].indexOf(ot(o)))throw new Error("options.mask must be a (Multi)Polygon");for(var s=e/ye([t[0],t[1]],[t[2],t[1]],n),c=s*(t[2]-t[0]),l=e/ye([t[0],t[1]],[t[0],t[3]],n),d=l*(t[3]-t[1]),p=0,f=t[0];f<=t[2];){for(var h=0,g=t[1];g<=t[3];){var m=null,y=null;p%2==0&&h%2==0?(m=i([[[f,g],[f,g+d],[f+c,g],[f,g]]],r),y=i([[[f,g+d],[f+c,g+d],[f+c,g],[f,g+d]]],r)):p%2==0&&h%2==1?(m=i([[[f,g],[f+c,g+d],[f+c,g],[f,g]]],r),y=i([[[f,g],[f,g+d],[f+c,g+d],[f,g]]],r)):h%2==0&&p%2==1?(m=i([[[f,g],[f,g+d],[f+c,g+d],[f,g]]],r),y=i([[[f,g],[f+c,g+d],[f+c,g],[f,g]]],r)):h%2==1&&p%2==1&&(m=i([[[f,g],[f,g+d],[f+c,g],[f,g]]],r),y=i([[[f,g+d],[f+c,g+d],[f+c,g],[f,g+d]]],r)),o?(xu(o,m)&&a.push(m),xu(o,y)&&a.push(y)):(a.push(m),a.push(y)),g+=d,h++}p++,f+=c}return u(a)}function ku(t,e,n){if("object"!=typeof(n=n||{}))throw new Error("options is invalid");var r=n.gridType,o=n.property,i=n.weight;if(!t)throw new Error("points is required");if(et(t,"Point","input must contain Points"),!e)throw new Error("cellSize is required");if(void 0!==i&&"number"!=typeof i)throw new Error("weight must be a number");o=o||"elevation",r=r||"square",i=i||1;var a,s=H(t);switch(r){case"point":case"points":a=Co(s,e,n);break;case"square":case"squares":a=Gu(s,e,n);break;case"hex":case"hexes":a=Pu(s,e,n);break;case"triangle":case"triangles":a=qu(s,e,n);break;default:throw new Error("invalid gridType")}var c=[];return q(a,function(e){var a=0,s=0;q(t,function(t){var c,u="point"===r?e:bn(e),l=ye(u,t,n);if(void 0!==o&&(c=t.properties[o]),void 0===c&&(c=t.geometry.coordinates[2]),void 0===c)throw new Error("zValue is missing");0===l&&(a=c);var d=1/Math.pow(l,i);s+=d,a+=d*c});var u=ve(e);u.properties[o]=a/s,c.push(u)}),u(c)}var Uu=6371008.8,Bu={meters:Uu,metres:Uu,millimeters:1e3*Uu,millimetres:1e3*Uu,centimeters:100*Uu,centimetres:100*Uu,kilometers:Uu/1e3,kilometres:Uu/1e3,miles:Uu/1609.344,nauticalmiles:Uu/1852,inches:39.37*Uu,yards:Uu/1.0936,feet:3.28084*Uu,radians:1,degrees:Uu/111325},Vu={meters:1,metres:1,millimeters:1e3,millimetres:1e3,centimeters:100,centimetres:100,kilometers:.001,kilometres:.001,miles:1/1609.344,nauticalmiles:1/1852,inches:39.37,yards:1/1.0936,feet:3.28084,radians:1/Uu,degrees:1/111325},zu={meters:1,metres:1,millimeters:1e6,millimetres:1e6,centimeters:1e4,centimetres:1e4,kilometers:1e-6,kilometres:1e-6,acres:247105e-9,miles:3.86e-7,yards:1.195990046,feet:10.763910417,inches:1550.003100006},ju=Object.freeze({earthRadius:Uu,factors:Bu,unitsFactors:Vu,areaFactors:zu,feature:e,geometry:n,point:r,points:o,polygon:i,polygons:a,lineString:s,lineStrings:c,featureCollection:u,multiLineString:l,multiPoint:d,multiPolygon:p,geometryCollection:f,round:h,radiansToLength:g,lengthToRadians:m,lengthToDegrees:y,bearingToAzimuth:v,radiansToDegrees:_,degreesToRadians:b,convertLength:w,convertArea:x,isNumber:E,isObject:C,validateBBox:S,validateId:I,radians2degrees:P,degrees2radians:A,distanceToDegrees:N,distanceToRadians:M,radiansToDistance:L,bearingToAngle:D,convertDistance:O}),Yu=Object.freeze({coordEach:T,coordReduce:R,propEach:F,propReduce:G,featureEach:q,featureReduce:k,coordAll:U,geomEach:B,geomReduce:V,flattenEach:z,flattenReduce:j,segmentEach:Y,segmentReduce:X,lineEach:W,lineReduce:$}),Xu=Object.freeze({getCoord:K,getCoords:J,containsNumber:Z,geojsonType:Q,featureOf:tt,collectionOf:et,getGeom:nt,getGeomType:rt,getType:ot}),Wu={successCallback:null,verbose:!1},$u={},Hu=_t,Ku=xt;xt.prototype={all:function(){return this._all(this.data,[])},search:function(t){var e=this.data,n=[],r=this.toBBox;if(!Tt(t,e))return n;for(var o,i,a,s,c=[];e;){for(o=0,i=e.children.length;o<i;o++)a=e.children[o],s=e.leaf?r(a):a,Tt(t,s)&&(e.leaf?n.push(a):Ot(t,s)?this._all(a,n):c.push(a));e=c.pop()}return n},collides:function(t){var e=this.data,n=this.toBBox;if(!Tt(t,e))return!1;for(var r,o,i,a,s=[];e;){for(r=0,o=e.children.length;r<o;r++)if(i=e.children[r],a=e.leaf?n(i):i,Tt(t,a)){if(e.leaf||Ot(t,a))return!0;s.push(i)}e=s.pop()}return!1},load:function(t){if(!t||!t.length)return this;if(t.length<this._minEntries){for(var e=0,n=t.length;e<n;e++)this.insert(t[e]);return this}var r=this._build(t.slice(),0,t.length-1,0);if(this.data.children.length)if(this.data.height===r.height)this._splitRoot(this.data,r);else{if(this.data.height<r.height){var o=this.data;this.data=r,r=o}this._insert(r,this.data.height-r.height-1,!0)}else this.data=r;return this},insert:function(t){return t&&this._insert(t,this.data.height-1),this},clear:function(){return this.data=Rt([]),this},remove:function(t,e){if(!t)return this;for(var n,r,o,i,a=this.data,s=this.toBBox(t),c=[],u=[];a||c.length;){if(a||(a=c.pop(),r=c[c.length-1],n=u.pop(),i=!0),a.leaf&&-1!==(o=Et(t,a.children,e)))return a.children.splice(o,1),c.push(a),this._condense(c),this;i||a.leaf||!Ot(a,s)?r?(n++,a=r.children[n],i=!1):a=null:(c.push(a),u.push(n),n=0,r=a,a=a.children[0])}return this},toBBox:function(t){return t},compareMinX:Pt,compareMinY:At,toJSON:function(){return this.data},fromJSON:function(t){return this.data=t,this},_all:function(t,e){for(var n=[];t;)t.leaf?e.push.apply(e,t.children):n.push.apply(n,t.children),t=n.pop();return e},_build:function(t,e,n,r){var o,i=n-e+1,a=this._maxEntries;if(i<=a)return o=Rt(t.slice(e,n+1)),Ct(o,this.toBBox),o;r||(r=Math.ceil(Math.log(i)/Math.log(a)),a=Math.ceil(i/Math.pow(a,r-1))),o=Rt([]),o.leaf=!1,o.height=r;var s,c,u,l,d=Math.ceil(i/a),p=d*Math.ceil(Math.sqrt(a));for(Ft(t,e,n,p,this.compareMinX),s=e;s<=n;s+=p)for(u=Math.min(s+p-1,n),Ft(t,s,u,d,this.compareMinY),c=s;c<=u;c+=d)l=Math.min(c+d-1,u),o.children.push(this._build(t,c,l,r-1));return Ct(o,this.toBBox),o},_chooseSubtree:function(t,e,n,r){for(var o,i,a,s,c,u,l,d;;){if(r.push(e),e.leaf||r.length-1===n)break;for(l=d=1/0,o=0,i=e.children.length;o<i;o++)a=e.children[o],c=Nt(a),u=Lt(t,a)-c,u<d?(d=u,l=c<l?c:l,s=a):u===d&&c<l&&(l=c,s=a);e=s||e.children[0]}return e},_insert:function(t,e,n){var r=this.toBBox,o=n?t:r(t),i=[],a=this._chooseSubtree(o,this.data,e,i);for(a.children.push(t),It(a,o);e>=0&&i[e].children.length>this._maxEntries;)this._split(i,e),e--;this._adjustParentBBoxes(o,i,e)},_split:function(t,e){var n=t[e],r=n.children.length,o=this._minEntries;this._chooseSplitAxis(n,o,r);var i=this._chooseSplitIndex(n,o,r),a=Rt(n.children.splice(i,n.children.length-i));a.height=n.height,a.leaf=n.leaf,Ct(n,this.toBBox),Ct(a,this.toBBox),e?t[e-1].children.push(a):this._splitRoot(n,a)},_splitRoot:function(t,e){this.data=Rt([t,e]),this.data.height=t.height+1,this.data.leaf=!1,Ct(this.data,this.toBBox)},_chooseSplitIndex:function(t,e,n){var r,o,i,a,s,c,u,l;for(c=u=1/0,r=e;r<=n-e;r++)o=St(t,0,r,this.toBBox),i=St(t,r,n,this.toBBox),a=Dt(o,i),s=Nt(o)+Nt(i),a<c?(c=a,l=r,u=s<u?s:u):a===c&&s<u&&(u=s,l=r);return l},_chooseSplitAxis:function(t,e,n){var r=t.leaf?this.compareMinX:Pt,o=t.leaf?this.compareMinY:At;this._allDistMargin(t,e,n,r)<this._allDistMargin(t,e,n,o)&&t.children.sort(r)},_allDistMargin:function(t,e,n,r){t.children.sort(r);var o,i,a=this.toBBox,s=St(t,0,e,a),c=St(t,n-e,n,a),u=Mt(s)+Mt(c);for(o=e;o<n-e;o++)i=t.children[o],It(s,t.leaf?a(i):i),u+=Mt(s);for(o=n-e-1;o>=e;o--)i=t.children[o],It(c,t.leaf?a(i):i),u+=Mt(c);return u},_adjustParentBBoxes:function(t,e,n){for(var r=n;r>=0;r--)It(e[r],t)},_condense:function(t){for(var e,n=t.length-1;n>=0;n--)0===t[n].children.length?n>0?(e=t[n-1].children,e.splice(e.indexOf(t[n]),1)):this.clear():Ct(t[n],this.toBBox)},_initFormat:function(t){var e=["return a"," - b",";"];this.compareMinX=new Function("a","b",e.join(t[0])),this.compareMinY=new Function("a","b",e.join(t[1])),this.toBBox=new Function("a","return {minX: a"+t[0]+", minY: a"+t[1]+", maxX: a"+t[2]+", maxY: a"+t[3]+"};")}};var Ju=qt,Zu=Number(Math.pow(2,27)+1),Qu=Ut,tl=Bt,el=Vt,nl=jt,rl=Gt(function(t){function e(t,e){for(var n=new Array(t.length-1),r=1;r<t.length;++r)for(var o=n[r-1]=new Array(t.length-1),i=0,a=0;i<t.length;++i)i!==e&&(o[a++]=t[r][i]);return n}function n(t){for(var e=new Array(t),n=0;n<t;++n){e[n]=new Array(t);for(var r=0;r<t;++r)e[n][r]=["m",r,"[",t-n-1,"]"].join("")}return e}function r(t){return 1&t?"-":""}function o(t){if(1===t.length)return t[0];if(2===t.length)return["sum(",t[0],",",t[1],")"].join("");var e=t.length>>1;return["sum(",o(t.slice(0,e)),",",o(t.slice(e)),")"].join("")}function i(t){if(2===t.length)return[["sum(prod(",t[0][0],",",t[1][1],"),prod(-",t[0][1],",",t[1][0],"))"].join("")];for(var n=[],a=0;a<t.length;++a)n.push(["scale(",o(i(e(t,a))),",",r(a),t[0][a],")"].join(""));return n}function a(t){for(var r=[],a=[],s=n(t),c=[],u=0;u<t;++u)0==(1&u)?r.push.apply(r,i(e(s,u))):a.push.apply(a,i(e(s,u))),c.push("m"+u);var l=o(r),d=o(a),p="orientation"+t+"Exact",f=["function ",p,"(",c.join(),"){var p=",l,",n=",d,",d=sub(p,n);return d[d.length-1];};return ",p].join("");return new Function("sum","prod","scale","sub",f)(Qu,Ju,el,nl)}function s(t){var e=d[t.length];return e||(e=d[t.length]=a(t.length)),e.apply(void 0,t)}var c=5,u=a(3),l=a(4),d=[function(){return 0},function(){return 0},function(t,e){return e[0]-t[0]},function(t,e,n){var r,o=(t[1]-n[1])*(e[0]-n[0]),i=(t[0]-n[0])*(e[1]-n[1]),a=o-i;if(o>0){if(i<=0)return a;r=o+i}else{if(!(o<0))return a;if(i>=0)return a;r=-(o+i)}var s=3.3306690738754716e-16*r;return a>=s||a<=-s?a:u(t,e,n)},function(t,e,n,r){var o=t[0]-r[0],i=e[0]-r[0],a=n[0]-r[0],s=t[1]-r[1],c=e[1]-r[1],u=n[1]-r[1],d=t[2]-r[2],p=e[2]-r[2],f=n[2]-r[2],h=i*u,g=a*c,m=a*s,y=o*u,v=o*c,_=i*s,b=d*(h-g)+p*(m-y)+f*(v-_),w=(Math.abs(h)+Math.abs(g))*Math.abs(d)+(Math.abs(m)+Math.abs(y))*Math.abs(p)+(Math.abs(v)+Math.abs(_))*Math.abs(f),x=7.771561172376103e-16*w;return b>x||-b>x?b:l(t,e,n,r)}];!function(){for(;d.length<=c;)d.push(a(d.length));for(var e=[],n=["slow"],r=0;r<=c;++r)e.push("a"+r),n.push("o"+r);for(var o=["function getOrientation(",e.join(),"){switch(arguments.length){case 0:case 1:return 0;"],r=2;r<=c;++r)o.push("case ",r,":return o",r,"(",e.slice(0,r).join(),");");o.push("}var s=new Array(arguments.length);for(var i=0;i<arguments.length;++i){s[i]=arguments[i]};return slow(s);}return getOrientation"),n.push(o.join(""));var i=Function.apply(void 0,n);t.exports=i.apply(void 0,[s].concat(d));for(var r=0;r<=c;++r)t.exports[r]=d[r]}()}),ol=Yt,il=rl[3],al=Xt,sl=Xt;Xt.prototype={push:function(t){this.data.push(t),this.length++,this._up(this.length-1)},pop:function(){if(0!==this.length){var t=this.data[0];return this.length--,this.length>0&&(this.data[0]=this.data[this.length],this._down(0)),this.data.pop(),t}},peek:function(){return this.data[0]},_up:function(t){for(var e=this.data,n=this.compare,r=e[t];t>0;){var o=t-1>>1,i=e[o];if(n(r,i)>=0)break;e[t]=i,t=o}e[t]=r},_down:function(t){for(var e=this.data,n=this.compare,r=this.length>>1,o=e[t];t<r;){var i=1+(t<<1),a=i+1,s=e[i];if(a<this.length&&n(e[a],s)<0&&(i=a,s=e[a]),n(s,o)>=0)break;e[t]=s,t=i}e[t]=o}},al.default=sl;var cl=function(t,e){for(var n=t[0],r=t[1],o=!1,i=0,a=e.length-1;i<e.length;a=i++){var s=e[i][0],c=e[i][1],u=e[a][0],l=e[a][1];c>r!=l>r&&n<(u-s)*(r-c)/(l-c)+s&&(o=!o)}return o},ul=rl[3],ll=$t,dl=$t;ll.default=dl;var pl=function(t){return t},fl=function(t){if(null==t)return pl;var e,n,r=t.scale[0],o=t.scale[1],i=t.translate[0],a=t.translate[1];return function(t,s){s||(e=n=0);var c=2,u=t.length,l=new Array(u);for(l[0]=(e+=t[0])*r+i,l[1]=(n+=t[1])*o+a;c<u;)l[c]=t[c],++c;return l}},hl=function(t,e){for(var n,r=t.length,o=r-e;o<--r;)n=t[o],t[o++]=t[r],t[r]=n},gl=function(t,e){function n(e){var n,r=t.arcs[e<0?~e:e],o=r[0];return t.transform?(n=[0,0],r.forEach(function(t){n[0]+=t[0],n[1]+=t[1]})):n=r[r.length-1],e<0?[n,o]:[o,n]}function r(t,e){for(var n in t){var r=t[n];delete e[r.start],delete r.start,delete r.end,r.forEach(function(t){o[t<0?~t:t]=1}),s.push(r)}}var o={},i={},a={},s=[],c=-1;return e.forEach(function(n,r){var o,i=t.arcs[n<0?~n:n];i.length<3&&!i[1][0]&&!i[1][1]&&(o=e[++c],e[c]=n,e[r]=o)}),e.forEach(function(t){var e,r,o=n(t),s=o[0],c=o[1];if(e=a[s])if(delete a[e.end],e.push(t),e.end=c,r=i[c]){delete i[r.start];var u=r===e?e:e.concat(r);i[u.start=e.start]=a[u.end=r.end]=u}else i[e.start]=a[e.end]=e;else if(e=i[c])if(delete i[e.start],e.unshift(t),e.start=s,r=a[s]){delete a[r.end];var l=r===e?e:r.concat(e);i[l.start=r.start]=a[l.end=e.end]=l}else i[e.start]=a[e.end]=e;else e=[t],i[e.start=s]=a[e.end=c]=e}),r(a,i),r(i,a),e.forEach(function(t){o[t<0?~t:t]||s.push([t])}),s},ml=function(t){return Ce(t,Ie.apply(this,arguments))},yl=function(t){function e(t){null!=t&&u.hasOwnProperty(t.type)&&u[t.type](t)}function n(t){var e=t[0],n=t[1];e<i&&(i=e),e>s&&(s=e),n<a&&(a=n),n>c&&(c=n)}function r(t){t.forEach(n)}function o(t){t.forEach(r)}var i=1/0,a=1/0,s=-1/0,c=-1/0,u={GeometryCollection:function(t){t.geometries.forEach(e)},Point:function(t){n(t.coordinates)},MultiPoint:function(t){t.coordinates.forEach(n)},LineString:function(t){r(t.arcs)},MultiLineString:function(t){t.arcs.forEach(r)},Polygon:function(t){t.arcs.forEach(r)},MultiPolygon:function(t){t.arcs.forEach(o)}};for(var l in t)e(t[l]);return s>=i&&c>=a?[i,a,s,c]:void 0},vl=function(t,e,n,r,o){function i(r){for(var i=e(r)&u,a=c[i],s=0;a!=o;){if(n(a,r))return!0;if(++s>=t)throw new Error("full hashset");a=c[i=i+1&u]}return c[i]=r,!0}function a(r){for(var i=e(r)&u,a=c[i],s=0;a!=o;){if(n(a,r))return!0;if(++s>=t)break;a=c[i=i+1&u]}return!1}function s(){for(var t=[],e=0,n=c.length;e<n;++e){var r=c[e];r!=o&&t.push(r)}return t}3===arguments.length&&(r=Array,o=null);for(var c=new r(t=1<<Math.max(4,Math.ceil(Math.log(t)/Math.LN2))),u=t-1,l=0;l<t;++l)c[l]=o;return{add:i,has:a,values:s}},_l=function(t,e,n,r,o,i){function a(r,i){for(var a=e(r)&p,s=l[a],c=0;s!=o;){if(n(s,r))return d[a]=i;if(++c>=t)throw new Error("full hashmap");s=l[a=a+1&p]}return l[a]=r,d[a]=i,i}function s(r,i){for(var a=e(r)&p,s=l[a],c=0;s!=o;){if(n(s,r))return d[a];if(++c>=t)throw new Error("full hashmap");s=l[a=a+1&p]}return l[a]=r,d[a]=i,i}function c(r,i){for(var a=e(r)&p,s=l[a],c=0;s!=o;){if(n(s,r))return d[a];if(++c>=t)break;s=l[a=a+1&p]}return i}function u(){for(var t=[],e=0,n=l.length;e<n;++e){var r=l[e];r!=o&&t.push(r)}return t}3===arguments.length&&(r=i=Array,o=null);for(var l=new r(t=1<<Math.max(4,Math.ceil(Math.log(t)/Math.LN2))),d=new i(t),p=t-1,f=0;f<t;++f)l[f]=o;return{set:a,maybeSet:s,get:c,keys:u}},bl=function(t,e){return t[0]===e[0]&&t[1]===e[1]},wl=new ArrayBuffer(16),xl=new Float64Array(wl),El=new Uint32Array(wl),Cl=function(t){xl[0]=t[0],xl[1]=t[1];var e=El[0]^El[1];return 2147483647&(e=e<<5^e>>7^El[2]^El[3])},Sl=function(t){function e(t,e,n,r){if(f[n]!==t){f[n]=t;var o=h[n];if(o>=0){var i=g[n];o===e&&i===r||o===r&&i===e||(++y,m[n]=1)}else h[n]=e,g[n]=r}}function n(t){return Cl(u[t])}function r(t,e){return bl(u[t],u[e])}var o,i,a,s,c,u=t.coordinates,l=t.lines,d=t.rings,p=function(){for(var t=_l(1.4*u.length,n,r,Int32Array,-1,Int32Array),e=new Int32Array(u.length),o=0,i=u.length;o<i;++o)e[o]=t.maybeSet(o,o);return e}(),f=new Int32Array(u.length),h=new Int32Array(u.length),g=new Int32Array(u.length),m=new Int8Array(u.length),y=0;for(o=0,i=u.length;o<i;++o)f[o]=h[o]=g[o]=-1;for(o=0,i=l.length;o<i;++o){var v=l[o],_=v[0],b=v[1];for(s=p[_],c=p[++_],++y,m[s]=1;++_<=b;)e(o,a=s,s=c,c=p[_]);++y,m[c]=1}for(o=0,i=u.length;o<i;++o)f[o]=-1;for(o=0,i=d.length;o<i;++o){var w=d[o],x=w[0]+1,E=w[1];for(a=p[E-1],s=p[x-1],c=p[x],e(o,a,s,c);++x<=E;)e(o,a=s,s=c,c=p[x])}f=h=g=null;var C,S=vl(1.4*y,Cl,bl);for(o=0,i=u.length;o<i;++o)m[C=p[o]]&&S.add(u[C]);return S},Il=function(t){var e,n,r,o=Sl(t),i=t.coordinates,a=t.lines,s=t.rings;for(n=0,r=a.length;n<r;++n)for(var c=a[n],u=c[0],l=c[1];++u<l;)o.has(i[u])&&(e={0:u,1:c[1]},c[1]=u,c=c.next=e);for(n=0,r=s.length;n<r;++n)for(var d=s[n],p=d[0],f=p,h=d[1],g=o.has(i[p]);++f<h;)o.has(i[f])&&(g?(e={0:f,1:d[1]},d[1]=f,d=d.next=e):(Pe(i,p,h,h-f),i[h]=i[p],g=!0,f=p));return t},Pl=function(t){function e(t){var e,o,i,a,s,c,u,l;if(i=g.get(e=d[t[0]]))for(u=0,l=i.length;u<l;++u)if(a=i[u],n(a,t))return t[0]=a[0],void(t[1]=a[1]);if(s=g.get(o=d[t[1]]))for(u=0,l=s.length;u<l;++u)if(c=s[u],r(c,t))return t[1]=c[0],void(t[0]=c[1]);i?i.push(t):g.set(e,[t]),s?s.push(t):g.set(o,[t]),m.push(t)}function n(t,e){var n=t[0],r=e[0],o=t[1];if(n-o!=r-e[1])return!1;for(;n<=o;++n,++r)if(!bl(d[n],d[r]))return!1;return!0}function r(t,e){var n=t[0],r=e[0],o=t[1],i=e[1];if(n-o!=r-i)return!1;for(;n<=o;++n,--i)if(!bl(d[n],d[i]))return!1;return!0}function o(t,e){var n=t[0],r=e[0],o=t[1],i=e[1],s=o-n;if(s!==i-r)return!1;for(var c=a(t),u=a(e),l=0;l<s;++l)if(!bl(d[n+(l+c)%s],d[r+(l+u)%s]))return!1;return!0}function i(t,e){var n=t[0],r=e[0],o=t[1],i=e[1],s=o-n;if(s!==i-r)return!1;for(var c=a(t),u=s-a(e),l=0;l<s;++l)if(!bl(d[n+(l+c)%s],d[i-(l+u)%s]))return!1;return!0}function a(t){for(var e=t[0],n=t[1],r=e,o=r,i=d[r];++r<n;){var a=d[r];(a[0]<i[0]||a[0]===i[0]&&a[1]<i[1])&&(o=r,i=a)}return o-e}var s,c,u,l,d=t.coordinates,p=t.lines,f=t.rings,h=p.length+f.length;for(delete t.lines,delete t.rings,u=0,l=p.length;u<l;++u)for(s=p[u];s=s.next;)++h;for(u=0,l=f.length;u<l;++u)for(c=f[u];c=c.next;)++h;var g=_l(2*h*1.4,Cl,bl),m=t.arcs=[];for(u=0,l=p.length;u<l;++u){s=p[u];do{e(s)}while(s=s.next)}for(u=0,l=f.length;u<l;++u)if(c=f[u],c.next)do{e(c)}while(c=c.next);else!function(t){var e,n,r,s,c;if(n=g.get(e=d[t[0]]))for(s=0,c=n.length;s<c;++s){if(r=n[s],o(r,t))return t[0]=r[0],void(t[1]=r[1]);if(i(r,t))return t[0]=r[1],void(t[1]=r[0])}if(n=g.get(e=d[t[0]+a(t)]))for(s=0,c=n.length;s<c;++s){if(r=n[s],o(r,t))return t[0]=r[0],void(t[1]=r[1]);if(i(r,t))return t[0]=r[1],void(t[1]=r[0])}n?n.push(t):g.set(e,[t]),m.push(t)}(c);return t},Al=function(t){for(var e=-1,n=t.length;++e<n;){for(var r,o,i=t[e],a=0,s=1,c=i.length,u=i[0],l=u[0],d=u[1];++a<c;)u=i[a],r=u[0],o=u[1],r===l&&o===d||(i[s++]=[r-l,o-d],l=r,d=o);1===s&&(i[s++]=[0,0]),i.length=s}return t},Nl=function(t){function e(t){t&&u.hasOwnProperty(t.type)&&u[t.type](t)}function n(t){for(var e=0,n=t.length;e<n;++e)c[++i]=t[e];var r={0:i-n+1,1:i};return a.push(r),r}function r(t){for(var e=0,n=t.length;e<n;++e)c[++i]=t[e];var r={0:i-n+1,1:i};return s.push(r),r}function o(t){return t.map(r)}var i=-1,a=[],s=[],c=[],u={GeometryCollection:function(t){t.geometries.forEach(e)},LineString:function(t){t.arcs=n(t.arcs)},MultiLineString:function(t){t.arcs=t.arcs.map(n)},Polygon:function(t){t.arcs=t.arcs.map(r)},MultiPolygon:function(t){t.arcs=t.arcs.map(o)}};for(var l in t)e(t[l]);return{type:"Topology",coordinates:c,lines:a,rings:s,objects:t}},Ml=function(t){var e,n={};for(e in t)n[e]=Ne(t[e]);return n},Ll=function(t,e,n){function r(t){return[Math.round((t[0]-u)*f),Math.round((t[1]-l)*h)]}function o(t,e){for(var n,r,o,i,a,s=-1,c=0,d=t.length,p=new Array(d);++s<d;)n=t[s],i=Math.round((n[0]-u)*f),a=Math.round((n[1]-l)*h),i===r&&a===o||(p[c++]=[r=i,o=a]);for(p.length=c;c<e;)c=p.push([p[0][0],p[0][1]]);return p}function i(t){return o(t,2)}function a(t){return o(t,4)}function s(t){return t.map(a)}function c(t){null!=t&&g.hasOwnProperty(t.type)&&g[t.type](t)}var u=e[0],l=e[1],d=e[2],p=e[3],f=d-u?(n-1)/(d-u):1,h=p-l?(n-1)/(p-l):1,g={GeometryCollection:function(t){t.geometries.forEach(c)},Point:function(t){t.coordinates=r(t.coordinates)},MultiPoint:function(t){t.coordinates=t.coordinates.map(r)},LineString:function(t){t.arcs=i(t.arcs)},MultiLineString:function(t){t.arcs=t.arcs.map(i)},Polygon:function(t){t.arcs=s(t.arcs)},MultiPolygon:function(t){t.arcs=t.arcs.map(s)}};for(var m in t)c(t[m]);return{scale:[1/f,1/h],translate:[u,l]}},Dl=function(t,e){function n(t){t&&l.hasOwnProperty(t.type)&&l[t.type](t)}function r(t){var e=[];do{var n=u.get(t);e.push(t[0]<t[1]?n:~n)}while(t=t.next);return e}function o(t){return t.map(r)}var i=yl(t=Ml(t)),a=e>0&&i&&Ll(t,i,e),s=Pl(Il(Nl(t))),c=s.coordinates,u=_l(1.4*s.arcs.length,Oe,Te);t=s.objects,s.bbox=i,s.arcs=s.arcs.map(function(t,e){return u.set(t,e),c.slice(t[0],t[1]+1)}),delete s.coordinates,c=null;var l={GeometryCollection:function(t){t.geometries.forEach(n)},LineString:function(t){t.arcs=r(t.arcs)},MultiLineString:function(t){t.arcs=t.arcs.map(r)},Polygon:function(t){t.arcs=t.arcs.map(r)},MultiPolygon:function(t){t.arcs=t.arcs.map(o)}};for(var d in t)n(t[d]);return a&&(s.transform=a,s.arcs=Al(s.arcs)),s},Ol=function(t){this.points=t.points||[],this.duration=t.duration||1e4,this.sharpness=t.sharpness||.85,this.centers=[],this.controls=[],this.stepLength=t.stepLength||60,this.length=this.points.length,this.delay=0;for(var e=0;e<this.length;e++)this.points[e].z=this.points[e].z||0;for(var e=0;e<this.length-1;e++){var n=this.points[e],r=this.points[e+1];this.centers.push({x:(n.x+r.x)/2,y:(n.y+r.y)/2,z:(n.z+r.z)/2})}this.controls.push([this.points[0],this.points[0]]);for(var e=0;e<this.centers.length-1;e++){var n=this.centers[e],r=this.centers[e+1],o=this.points[e+1].x-(this.centers[e].x+this.centers[e+1].x)/2,i=this.points[e+1].y-(this.centers[e].y+this.centers[e+1].y)/2,a=this.points[e+1].z-(this.centers[e].y+this.centers[e+1].z)/2;this.controls.push([{x:(1-this.sharpness)*this.points[e+1].x+this.sharpness*(this.centers[e].x+o),y:(1-this.sharpness)*this.points[e+1].y+this.sharpness*(this.centers[e].y+i),z:(1-this.sharpness)*this.points[e+1].z+this.sharpness*(this.centers[e].z+a)},{x:(1-this.sharpness)*this.points[e+1].x+this.sharpness*(this.centers[e+1].x+o),y:(1-this.sharpness)*this.points[e+1].y+this.sharpness*(this.centers[e+1].y+i),z:(1-this.sharpness)*this.points[e+1].z+this.sharpness*(this.centers[e+1].z+a)}])}return this.controls.push([this.points[this.length-1],this.points[this.length-1]]),this.steps=this.cacheSteps(this.stepLength),this};Ol.prototype.cacheSteps=function(t){var e=[],n=this.pos(0);e.push(0);for(var r=0;r<this.duration;r+=10){var o=this.pos(r);Math.sqrt((o.x-n.x)*(o.x-n.x)+(o.y-n.y)*(o.y-n.y)+(o.z-n.z)*(o.z-n.z))>t&&(e.push(r),n=o)}return e},Ol.prototype.vector=function(t){var e=this.pos(t+10),n=this.pos(t-10);return{angle:180*Math.atan2(e.y-n.y,e.x-n.x)/3.14,speed:Math.sqrt((n.x-e.x)*(n.x-e.x)+(n.y-e.y)*(n.y-e.y)+(n.z-e.z)*(n.z-e.z))}},Ol.prototype.pos=function(t){var e=t-this.delay;e<0&&(e=0),e>this.duration&&(e=this.duration-1);var n=e/this.duration;if(n>=1)return this.points[this.length-1];var r=Math.floor((this.points.length-1)*n),o=(this.length-1)*n-r;return function(t,e,n,r,o){var i=function(t){var e=t*t;return[e*t,3*e*(1-t),3*t*(1-t)*(1-t),(1-t)*(1-t)*(1-t)]}(t);return{x:o.x*i[0]+r.x*i[1]+n.x*i[2]+e.x*i[3],y:o.y*i[0]+r.y*i[1]+n.y*i[2]+e.y*i[3],z:o.z*i[0]+r.z*i[1]+n.z*i[2]+e.z*i[3]}}(o,this.points[r],this.controls[r][1],this.controls[r+1][0],this.points[r+1])};var Tl=Cn,Rl=Cn;Cn.deviation=function(t,e,n,r){var o=e&&e.length,i=o?e[0]*n:t.length,a=Math.abs(Zn(t,0,i,n));if(o)for(var s=0,c=e.length;s<c;s++){var u=e[s]*n,l=s<c-1?e[s+1]*n:t.length;a-=Math.abs(Zn(t,u,l,n))}var d=0;for(s=0;s<r.length;s+=3){var p=r[s]*n,f=r[s+1]*n,h=r[s+2]*n;d+=Math.abs((t[p]-t[h])*(t[f+1]-t[p+1])-(t[p]-t[f])*(t[h+1]-t[p+1]))}return 0===a&&0===d?0:Math.abs((d-a)/a)},Cn.flatten=function(t){for(var e=t[0][0].length,n={vertices:[],holes:[],dimensions:e},r=0,o=0;o<t.length;o++){for(var i=0;i<t[o].length;i++)for(var a=0;a<e;a++)n.vertices.push(t[o][i][a]);o>0&&(r+=t[o-1].length,n.holes.push(r))}return n},Tl.default=Rl,sr.prototype={all:function(){return this._all(this.data,[])},search:function(t){var e=this.data,n=[],r=this.toBBox;if(!_r(t,e))return n;for(var o,i,a,s,c=[];e;){for(o=0,i=e.children.length;o<i;o++)a=e.children[o],s=e.leaf?r(a):a,_r(t,s)&&(e.leaf?n.push(a):vr(t,s)?this._all(a,n):c.push(a));e=c.pop()}return n},collides:function(t){var e=this.data,n=this.toBBox;if(!_r(t,e))return!1;for(var r,o,i,a,s=[];e;){for(r=0,o=e.children.length;r<o;r++)if(i=e.children[r],a=e.leaf?n(i):i,_r(t,a)){if(e.leaf||vr(t,a))return!0;s.push(i)}e=s.pop()}return!1},load:function(t){if(!t||!t.length)return this;if(t.length<this._minEntries){for(var e=0,n=t.length;e<n;e++)this.insert(t[e]);return this}var r=this._build(t.slice(),0,t.length-1,0);if(this.data.children.length)if(this.data.height===r.height)this._splitRoot(this.data,r);else{if(this.data.height<r.height){var o=this.data;this.data=r,r=o}this._insert(r,this.data.height-r.height-1,!0)}else this.data=r;return this},insert:function(t){return t&&this._insert(t,this.data.height-1),this},clear:function(){return this.data=br([]),this},remove:function(t,e){if(!t)return this;for(var n,r,o,i,a=this.data,s=this.toBBox(t),c=[],u=[];a||c.length;){if(a||(a=c.pop(),r=c[c.length-1],n=u.pop(),i=!0),a.leaf&&-1!==(o=cr(t,a.children,e)))return a.children.splice(o,1),c.push(a),this._condense(c),this;i||a.leaf||!vr(a,s)?r?(n++,a=r.children[n],i=!1):a=null:(c.push(a),u.push(n),n=0,r=a,a=a.children[0])}return this},toBBox:function(t){return t},compareMinX:pr,compareMinY:fr,toJSON:function(){return this.data},fromJSON:function(t){return this.data=t,this},_all:function(t,e){for(var n=[];t;)t.leaf?e.push.apply(e,t.children):n.push.apply(n,t.children),t=n.pop();return e},_build:function(t,e,n,r){var o,i=n-e+1,a=this._maxEntries;if(i<=a)return o=br(t.slice(e,n+1)),ur(o,this.toBBox),o;r||(r=Math.ceil(Math.log(i)/Math.log(a)),a=Math.ceil(i/Math.pow(a,r-1))),o=br([]),o.leaf=!1,o.height=r;var s,c,u,l,d=Math.ceil(i/a),p=d*Math.ceil(Math.sqrt(a));for(wr(t,e,n,p,this.compareMinX),s=e;s<=n;s+=p)for(u=Math.min(s+p-1,n),wr(t,s,u,d,this.compareMinY),c=s;c<=u;c+=d)l=Math.min(c+d-1,u),o.children.push(this._build(t,c,l,r-1));return ur(o,this.toBBox),o},_chooseSubtree:function(t,e,n,r){for(var o,i,a,s,c,u,l,d;;){if(r.push(e),e.leaf||r.length-1===n)break;for(l=d=1/0,o=0,i=e.children.length;o<i;o++)a=e.children[o],c=hr(a),u=mr(t,a)-c,u<d?(d=u,l=c<l?c:l,s=a):u===d&&c<l&&(l=c,s=a);e=s||e.children[0]}return e},_insert:function(t,e,n){var r=this.toBBox,o=n?t:r(t),i=[],a=this._chooseSubtree(o,this.data,e,i);for(a.children.push(t),dr(a,o);e>=0&&i[e].children.length>this._maxEntries;)this._split(i,e),e--;this._adjustParentBBoxes(o,i,e)},_split:function(t,e){var n=t[e],r=n.children.length,o=this._minEntries;this._chooseSplitAxis(n,o,r);var i=this._chooseSplitIndex(n,o,r),a=br(n.children.splice(i,n.children.length-i));a.height=n.height,a.leaf=n.leaf,ur(n,this.toBBox),ur(a,this.toBBox),e?t[e-1].children.push(a):this._splitRoot(n,a)},_splitRoot:function(t,e){this.data=br([t,e]),this.data.height=t.height+1,this.data.leaf=!1,ur(this.data,this.toBBox)},_chooseSplitIndex:function(t,e,n){var r,o,i,a,s,c,u,l;for(c=u=1/0,r=e;r<=n-e;r++)o=lr(t,0,r,this.toBBox),i=lr(t,r,n,this.toBBox),a=yr(o,i),s=hr(o)+hr(i),a<c?(c=a,l=r,u=s<u?s:u):a===c&&s<u&&(u=s,l=r);return l},_chooseSplitAxis:function(t,e,n){var r=t.leaf?this.compareMinX:pr,o=t.leaf?this.compareMinY:fr;this._allDistMargin(t,e,n,r)<this._allDistMargin(t,e,n,o)&&t.children.sort(r)},_allDistMargin:function(t,e,n,r){t.children.sort(r);var o,i,a=this.toBBox,s=lr(t,0,e,a),c=lr(t,n-e,n,a),u=gr(s)+gr(c);for(o=e;o<n-e;o++)i=t.children[o],dr(s,t.leaf?a(i):i),u+=gr(s);for(o=n-e-1;o>=e;o--)i=t.children[o],dr(c,t.leaf?a(i):i),u+=gr(c);return u},_adjustParentBBoxes:function(t,e,n){for(var r=n;r>=0;r--)dr(e[r],t)},_condense:function(t){for(var e,n=t.length-1;n>=0;n--)0===t[n].children.length?n>0?(e=t[n-1].children,
e.splice(e.indexOf(t[n]),1)):this.clear():ur(t[n],this.toBBox)},_initFormat:function(t){var e=["return a"," - b",";"];this.compareMinX=new Function("a","b",e.join(t[0])),this.compareMinY=new Function("a","b",e.join(t[1])),this.toBBox=new Function("a","return {minX: a"+t[0]+", minY: a"+t[1]+", maxX: a"+t[2]+", maxY: a"+t[3]+"};")}};var Fl=Object.freeze({toMercator:Fr,toWgs84:Gr}),Gl=6378137,ql=function(t,e,n){function r(t,n,r,o){var c=i[t][n],u=i[t][n+1],l=i[r][o],d=i[r][o+1],p=Mo(c,u,l,d);if(null!==p){var f,h;if(f=u[0]!==c[0]?(p[0]-c[0])/(u[0]-c[0]):(p[1]-c[1])/(u[1]-c[1]),h=d[0]!==l[0]?(p[0]-l[0])/(d[0]-l[0]):(p[1]-l[1])/(d[1]-l[1]),!(f>=1||f<=0||h>=1||h<=0)){var g=p,m=!s[g];m&&(s[g]=!0),e?a.push(e(p,t,n,c,u,f,r,o,l,d,h,m)):a.push(p)}}}function o(t,e){var n,r,o,a,s=i[t][e],c=i[t][e+1];return s[0]<c[0]?(n=s[0],r=c[0]):(n=c[0],r=s[0]),s[1]<c[1]?(o=s[1],a=c[1]):(o=c[1],a=s[1]),{minX:n,minY:o,maxX:r,maxY:a,ring:t,edge:e}}if("Polygon"!==t.geometry.type)throw new Error("The input feature must be a Polygon");void 0===n&&(n=1);var i=t.geometry.coordinates,a=[],s={};if(n){for(var c=[],u=0;u<i.length;u++)for(var l=0;l<i[u].length-1;l++)c.push(o(u,l));var d=Ku();d.load(c)}for(var p=0;p<i.length;p++)for(var f=0;f<i[p].length-1;f++)if(n){var h=d.search(o(p,f));h.forEach(function(t){var e=t.ring,n=t.edge;r(p,f,e,n)})}else for(var g=0;g<i.length;g++)for(var m=0;m<i[g].length-1;m++)r(p,f,g,m);return e||(a={type:"Feature",geometry:{type:"MultiPoint",coordinates:a}}),a},kl=function(t){function e(){for(var t=[],e=0;e<h.features.length;e++)-1==h.features[e].properties.parent&&t.push(e);if(t.length>1)for(var e=0;e<t.length;e++){for(var n=-1,r=0;r<h.features.length;r++)t[e]!=r&&ce(h.features[t[e]].geometry.coordinates[0][0],h.features[r],{ignoreBoundary:!0})&&eo(h.features[r])<1/0&&(n=r);h.features[t[e]].properties.parent=n}}function n(){for(var t=0;t<h.features.length;t++)if(-1==h.features[t].properties.parent){var e=h.features[t].properties.winding;h.features[t].properties.netWinding=e,r(t,e)}}function r(t,e){for(var n=0;n<h.features.length;n++)if(h.features[n].properties.parent==t){var o=e+h.features[n].properties.winding;h.features[n].properties.netWinding=o,r(n,o)}}if("Feature"!=t.type)throw new Error("The input must a geojson object of type Feature");if(void 0===t.geometry||null==t.geometry)throw new Error("The input must a geojson object with a non-empty geometry");if("Polygon"!=t.geometry.type)throw new Error("The input must be a geojson Polygon");for(var o=t.geometry.coordinates.length,a=[],s=0;s<o;s++){var c=t.geometry.coordinates[s];To(c[0],c[c.length-1])||c.push(c[0]),a.push.apply(a,c.slice(0,c.length-1))}if(!Ro(a))throw new Error("The input polygon may not have duplicate vertices (except for the first and last vertex of each ring)");var l=a.length,d=ql(t,function(t,e,n,r,o,i,a,s,c,u,l,d){return[t,e,n,r,o,i,a,s,c,u,l,d]}),p=d.length;if(0==p){for(var f=[],s=0;s<o;s++)f.push(i([t.geometry.coordinates[s]],{parent:-1,winding:Oo(t.geometry.coordinates[s])}));var h=u(f);return e(),n(),h}for(var g=[],m=[],s=0;s<o;s++){g.push([]);for(var y=0;y<t.geometry.coordinates[s].length-1;y++)g[s].push([new Ul(t.geometry.coordinates[s][(y+1).modulo(t.geometry.coordinates[s].length-1)],1,[s,y],[s,(y+1).modulo(t.geometry.coordinates[s].length-1)],void 0)]),m.push(new Bl(t.geometry.coordinates[s][y],[s,(y-1).modulo(t.geometry.coordinates[s].length-1)],[s,y],void 0,void 0,!1,!0))}for(var s=0;s<p;s++)g[d[s][1]][d[s][2]].push(new Ul(d[s][0],d[s][5],[d[s][1],d[s][2]],[d[s][6],d[s][7]],void 0)),d[s][11]&&m.push(new Bl(d[s][0],[d[s][1],d[s][2]],[d[s][6],d[s][7]],void 0,void 0,!0,!0));for(var v=m.length,s=0;s<g.length;s++)for(var y=0;y<g[s].length;y++)g[s][y].sort(function(t,e){return t.param<e.param?-1:1});for(var _=[],s=0;s<v;s++)_.push({minX:m[s].coord[0],minY:m[s].coord[1],maxX:m[s].coord[0],maxY:m[s].coord[1],index:s});var b=Ku();b.load(_);for(var s=0;s<g.length;s++)for(var y=0;y<g[s].length;y++)for(var w=0;w<g[s][y].length;w++){var x;x=w==g[s][y].length-1?g[s][(y+1).modulo(t.geometry.coordinates[s].length-1)][0].coord:g[s][y][w+1].coord;var E=b.search({minX:x[0],minY:x[1],maxX:x[0],maxY:x[1]})[0];g[s][y][w].nxtIsectAlongEdgeIn=E.index}for(var s=0;s<g.length;s++)for(var y=0;y<g[s].length;y++)for(var w=0;w<g[s][y].length;w++){var x=g[s][y][w].coord,E=b.search({minX:x[0],minY:x[1],maxX:x[0],maxY:x[1]})[0],C=E.index;C<l?m[C].nxtIsectAlongRingAndEdge2=g[s][y][w].nxtIsectAlongEdgeIn:To(m[C].ringAndEdge1,g[s][y][w].ringAndEdgeIn)?m[C].nxtIsectAlongRingAndEdge1=g[s][y][w].nxtIsectAlongEdgeIn:m[C].nxtIsectAlongRingAndEdge2=g[s][y][w].nxtIsectAlongEdgeIn}for(var S=[],s=0,y=0;y<o;y++){for(var I=s,w=0;w<t.geometry.coordinates[y].length-1;w++)m[s].coord[0]<m[I].coord[0]&&(I=s),s++;for(var P=m[I].nxtIsectAlongRingAndEdge2,w=0;w<m.length;w++)if(m[w].nxtIsectAlongRingAndEdge1==I||m[w].nxtIsectAlongRingAndEdge2==I){var A=w;break}var N=Do([m[A].coord,m[I].coord,m[P].coord],!0)?1:-1;S.push({isect:I,parent:-1,winding:N})}S.sort(function(t,e){return m[t.isect].coord>m[e.isect].coord?-1:1});for(var f=[];S.length>0;){var M=S.pop(),L=M.isect,D=M.parent,O=M.winding,T=f.length,R=[m[L].coord],F=L;if(m[L].ringAndEdge1Walkable)var G=m[L].ringAndEdge1,q=m[L].nxtIsectAlongRingAndEdge1;else var G=m[L].ringAndEdge2,q=m[L].nxtIsectAlongRingAndEdge2;for(;!To(m[L].coord,m[q].coord);){R.push(m[q].coord);for(var k=void 0,s=0;s<S.length;s++)if(S[s].isect==q){k=s;break}if(void 0!=k&&S.splice(k,1),To(G,m[q].ringAndEdge1)){if(G=m[q].ringAndEdge2,m[q].ringAndEdge2Walkable=!1,m[q].ringAndEdge1Walkable){var U={isect:q};Do([m[F].coord,m[q].coord,m[m[q].nxtIsectAlongRingAndEdge2].coord],1==O)?(U.parent=D,U.winding=-O):(U.parent=T,U.winding=O),S.push(U)}F=q,q=m[q].nxtIsectAlongRingAndEdge2}else{if(G=m[q].ringAndEdge1,m[q].ringAndEdge1Walkable=!1,m[q].ringAndEdge2Walkable){var U={isect:q};Do([m[F].coord,m[q].coord,m[m[q].nxtIsectAlongRingAndEdge1].coord],1==O)?(U.parent=D,U.winding=-O):(U.parent=T,U.winding=O),S.push(U)}F=q,q=m[q].nxtIsectAlongRingAndEdge1}}R.push(m[q].coord),f.push(i([R],{index:T,parent:D,winding:O,netWinding:void 0}))}var h=u(f);return e(),n(),h},Ul=function(t,e,n,r,o){this.coord=t,this.param=e,this.ringAndEdgeIn=n,this.ringAndEdgeOut=r,this.nxtIsectAlongEdgeIn=o},Bl=function(t,e,n,r,o,i,a){this.coord=t,this.ringAndEdge1=e,this.ringAndEdge2=n,this.nxtIsectAlongRingAndEdge1=r,this.nxtIsectAlongRingAndEdge2=o,this.ringAndEdge1Walkable=i,this.ringAndEdge2Walkable=a};Number.prototype.modulo=function(t){return(this%t+t)%t};var Vl=Math.PI/180,zl=180/Math.PI,jl=function(t,e){this.lon=t,this.lat=e,this.x=Vl*t,this.y=Vl*e};jl.prototype.view=function(){return String(this.lon).slice(0,4)+","+String(this.lat).slice(0,4)},jl.prototype.antipode=function(){var t=-1*this.lat,e=this.lon<0?180+this.lon:-1*(180-this.lon);return new jl(e,t)};var Yl=function(){this.coords=[],this.length=0};Yl.prototype.move_to=function(t){this.length++,this.coords.push(t)};var Xl=function(t){this.properties=t||{},this.geometries=[]};Xl.prototype.json=function(){if(this.geometries.length<=0)return{geometry:{type:"LineString",coordinates:null},type:"Feature",properties:this.properties};if(1===this.geometries.length)return{geometry:{type:"LineString",coordinates:this.geometries[0].coords},type:"Feature",properties:this.properties};for(var t=[],e=0;e<this.geometries.length;e++)t.push(this.geometries[e].coords);return{geometry:{type:"MultiLineString",coordinates:t},type:"Feature",properties:this.properties}},Xl.prototype.wkt=function(){for(var t="",e="LINESTRING(",n=function(t){e+=t[0]+" "+t[1]+","},r=0;r<this.geometries.length;r++){if(0===this.geometries[r].coords.length)return"LINESTRING(empty)";this.geometries[r].coords.forEach(n),t+=e.substring(0,e.length-1)+")"}return t};var Wl=function(t,e,n){if(!t||void 0===t.x||void 0===t.y)throw new Error("GreatCircle constructor expects two args: start and end objects with x and y properties");if(!e||void 0===e.x||void 0===e.y)throw new Error("GreatCircle constructor expects two args: start and end objects with x and y properties");this.start=new jl(t.x,t.y),this.end=new jl(e.x,e.y),this.properties=n||{};var r=this.start.x-this.end.x,o=this.start.y-this.end.y,i=Math.pow(Math.sin(o/2),2)+Math.cos(this.start.y)*Math.cos(this.end.y)*Math.pow(Math.sin(r/2),2);if(this.g=2*Math.asin(Math.sqrt(i)),this.g===Math.PI)throw new Error("it appears "+t.view()+" and "+e.view()+" are 'antipodal', e.g diametrically opposite, thus there is no single route but rather infinite");if(isNaN(this.g))throw new Error("could not calculate great circle between "+t+" and "+e)};Wl.prototype.interpolate=function(t){var e=Math.sin((1-t)*this.g)/Math.sin(this.g),n=Math.sin(t*this.g)/Math.sin(this.g),r=e*Math.cos(this.start.y)*Math.cos(this.start.x)+n*Math.cos(this.end.y)*Math.cos(this.end.x),o=e*Math.cos(this.start.y)*Math.sin(this.start.x)+n*Math.cos(this.end.y)*Math.sin(this.end.x),i=e*Math.sin(this.start.y)+n*Math.sin(this.end.y),a=zl*Math.atan2(i,Math.sqrt(Math.pow(r,2)+Math.pow(o,2)));return[zl*Math.atan2(o,r),a]},Wl.prototype.Arc=function(t,e){var n=[];if(!t||t<=2)n.push([this.start.lon,this.start.lat]),n.push([this.end.lon,this.end.lat]);else for(var r=1/(t-1),o=0;o<t;++o){var i=r*o,a=this.interpolate(i);n.push(a)}for(var s=!1,c=0,u=e&&e.offset?e.offset:10,l=180-u,d=-180+u,p=360-u,f=1;f<n.length;++f){var h=n[f-1][0],g=n[f][0],m=Math.abs(g-h);m>p&&(g>l&&h<d||h>l&&g<d)?s=!0:m>c&&(c=m)}var y=[];if(s&&c<u){var v=[];y.push(v);for(var _=0;_<n.length;++_){var b=parseFloat(n[_][0]);if(_>0&&Math.abs(b-n[_-1][0])>p){var w=parseFloat(n[_-1][0]),x=parseFloat(n[_-1][1]),E=parseFloat(n[_][0]),C=parseFloat(n[_][1]);if(w>-180&&w<d&&180===E&&_+1<n.length&&n[_-1][0]>-180&&n[_-1][0]<d){v.push([-180,n[_][1]]),_++,v.push([n[_][0],n[_][1]]);continue}if(w>l&&w<180&&-180===E&&_+1<n.length&&n[_-1][0]>l&&n[_-1][0]<180){v.push([180,n[_][1]]),_++,v.push([n[_][0],n[_][1]]);continue}if(w<d&&E>l){var S=w;w=E,E=S;var I=x;x=C,C=I}if(w>l&&E<d&&(E+=360),w<=180&&E>=180&&w<E){var P=(180-w)/(E-w),A=P*C+(1-P)*x;v.push([n[_-1][0]>l?180:-180,A]),v=[],v.push([n[_-1][0]>l?-180:180,A]),y.push(v)}else v=[],y.push(v);v.push([b,n[_][1]])}else v.push([n[_][0],n[_][1]])}}else{var N=[];y.push(N);for(var M=0;M<n.length;++M)N.push([n[M][0],n[M][1]])}for(var L=new Xl(this.properties),D=0;D<y.length;++D){var O=new Yl;L.geometries.push(O);for(var T=y[D],R=0;R<T.length;++R)O.move_to(T[R])}return L};var $l=Jo;Jo.polyline=Jo,Jo.polygon=Zo;var Hl=Array.prototype.slice,Kl={successCallback:null,verbose:!1,polygons:!1},Jl={},Zl=64,Ql=16,td=4,ed=1,nd=[],rd=[],od=[],id=[],ad=[],sd=[],cd=[],ud=[],ld=[],dd=[],pd=[],fd=[],hd=[],gd=[],md=[],yd=[],vd=[],_d=[],bd=[],wd=[],xd=[],Ed=[],Cd=[],Sd=[];cd[85]=dd[85]=-1,ud[85]=pd[85]=0,ld[85]=fd[85]=1,bd[85]=Ed[85]=1,wd[85]=Cd[85]=0,xd[85]=Sd[85]=1,nd[85]=id[85]=0,rd[85]=ad[85]=-1,od[85]=md[85]=0,yd[85]=hd[85]=0,vd[85]=gd[85]=1,sd[85]=_d[85]=1,Ed[1]=Ed[169]=0,Cd[1]=Cd[169]=-1,Sd[1]=Sd[169]=0,hd[1]=hd[169]=-1,gd[1]=gd[169]=0,md[1]=md[169]=0,dd[4]=dd[166]=0,pd[4]=pd[166]=-1,fd[4]=fd[166]=1,yd[4]=yd[166]=1,vd[4]=vd[166]=0,_d[4]=_d[166]=0,cd[16]=cd[154]=0,ud[16]=ud[154]=1,ld[16]=ld[154]=1,id[16]=id[154]=1,ad[16]=ad[154]=0,sd[16]=sd[154]=1,bd[64]=bd[106]=0,wd[64]=wd[106]=1,xd[64]=xd[106]=0,nd[64]=nd[106]=-1,rd[64]=rd[106]=0,od[64]=od[106]=1,bd[2]=bd[168]=0,wd[2]=wd[168]=-1,xd[2]=xd[168]=1,Ed[2]=Ed[168]=0,Cd[2]=Cd[168]=-1,Sd[2]=Sd[168]=0,hd[2]=hd[168]=-1,gd[2]=gd[168]=0,md[2]=md[168]=0,yd[2]=yd[168]=-1,vd[2]=vd[168]=0,_d[2]=_d[168]=1,cd[8]=cd[162]=0,ud[8]=ud[162]=-1,ld[8]=ld[162]=0,dd[8]=dd[162]=0,pd[8]=pd[162]=-1,fd[8]=fd[162]=1,hd[8]=hd[162]=1,gd[8]=gd[162]=0,md[8]=md[162]=1,yd[8]=yd[162]=1,vd[8]=vd[162]=0,_d[8]=_d[162]=0,cd[32]=cd[138]=0,ud[32]=ud[138]=1,ld[32]=ld[138]=1,dd[32]=dd[138]=0,pd[32]=pd[138]=1,fd[32]=fd[138]=0,nd[32]=nd[138]=1,rd[32]=rd[138]=0,od[32]=od[138]=0,id[32]=id[138]=1,ad[32]=ad[138]=0,sd[32]=sd[138]=1,Ed[128]=Ed[42]=0,Cd[128]=Cd[42]=1,Sd[128]=Sd[42]=1,bd[128]=bd[42]=0,wd[128]=wd[42]=1,xd[128]=xd[42]=0,nd[128]=nd[42]=-1,rd[128]=rd[42]=0,od[128]=od[42]=1,id[128]=id[42]=-1,ad[128]=ad[42]=0,sd[128]=sd[42]=0,dd[5]=dd[165]=-1,pd[5]=pd[165]=0,fd[5]=fd[165]=0,Ed[5]=Ed[165]=1,Cd[5]=Cd[165]=0,Sd[5]=Sd[165]=0,yd[20]=yd[150]=0,vd[20]=vd[150]=1,_d[20]=_d[150]=1,id[20]=id[150]=0,ad[20]=ad[150]=-1,sd[20]=sd[150]=1,cd[80]=cd[90]=-1,ud[80]=ud[90]=0,ld[80]=ld[90]=1,bd[80]=bd[90]=1,wd[80]=wd[90]=0,xd[80]=xd[90]=1,hd[65]=hd[105]=0,gd[65]=gd[105]=1,md[65]=md[105]=0,nd[65]=nd[105]=0,rd[65]=rd[105]=-1,od[65]=od[105]=0,cd[160]=cd[10]=-1,ud[160]=ud[10]=0,ld[160]=ld[10]=1,dd[160]=dd[10]=-1,pd[160]=pd[10]=0,fd[160]=fd[10]=0,Ed[160]=Ed[10]=1,Cd[160]=Cd[10]=0,Sd[160]=Sd[10]=0,bd[160]=bd[10]=1,wd[160]=wd[10]=0,xd[160]=xd[10]=1,yd[130]=yd[40]=0,vd[130]=vd[40]=1,_d[130]=_d[40]=1,hd[130]=hd[40]=0,gd[130]=gd[40]=1,md[130]=md[40]=0,nd[130]=nd[40]=0,rd[130]=rd[40]=-1,od[130]=od[40]=0,id[130]=id[40]=0,ad[130]=ad[40]=-1,sd[130]=sd[40]=1,dd[37]=dd[133]=0,pd[37]=pd[133]=1,fd[37]=fd[133]=1,Ed[37]=Ed[133]=0,Cd[37]=Cd[133]=1,Sd[37]=Sd[133]=0,nd[37]=nd[133]=-1,rd[37]=rd[133]=0,od[37]=od[133]=0,id[37]=id[133]=1,ad[37]=ad[133]=0,sd[37]=sd[133]=0,yd[148]=yd[22]=-1,vd[148]=vd[22]=0,_d[148]=_d[22]=0,Ed[148]=Ed[22]=0,Cd[148]=Cd[22]=-1,Sd[148]=Sd[22]=1,bd[148]=bd[22]=0,wd[148]=wd[22]=1,xd[148]=xd[22]=1,id[148]=id[22]=-1,ad[148]=ad[22]=0,sd[148]=sd[22]=1,cd[82]=cd[88]=0,ud[82]=ud[88]=-1,ld[82]=ld[88]=1,yd[82]=yd[88]=1,vd[82]=vd[88]=0,_d[82]=_d[88]=1,hd[82]=hd[88]=-1,gd[82]=gd[88]=0,md[82]=md[88]=1,bd[82]=bd[88]=0,wd[82]=wd[88]=-1,xd[82]=xd[88]=0,cd[73]=cd[97]=0,ud[73]=ud[97]=1,ld[73]=ld[97]=0,dd[73]=dd[97]=0,pd[73]=pd[97]=-1,fd[73]=fd[97]=0,hd[73]=hd[97]=1,gd[73]=gd[97]=0,md[73]=md[97]=0,nd[73]=nd[97]=1,rd[73]=rd[97]=0,od[73]=od[97]=1,cd[145]=cd[25]=0,ud[145]=ud[25]=-1,ld[145]=ld[25]=0,hd[145]=hd[25]=1,gd[145]=gd[25]=0,md[145]=md[25]=1,Ed[145]=Ed[25]=0,Cd[145]=Cd[25]=1,Sd[145]=Sd[25]=1,id[145]=id[25]=-1,ad[145]=ad[25]=0,sd[145]=sd[25]=0,dd[70]=dd[100]=0,pd[70]=pd[100]=1,fd[70]=fd[100]=0,yd[70]=yd[100]=-1,vd[70]=vd[100]=0,_d[70]=_d[100]=1,bd[70]=bd[100]=0,wd[70]=wd[100]=-1;xd[70]=xd[100]=1,nd[70]=nd[100]=1,rd[70]=rd[100]=0,od[70]=od[100]=0,dd[101]=dd[69]=0,pd[101]=pd[69]=1,fd[101]=fd[69]=0,nd[101]=nd[69]=1,rd[101]=rd[69]=0,od[101]=od[69]=0,Ed[149]=Ed[21]=0,Cd[149]=Cd[21]=1,Sd[149]=Sd[21]=1,id[149]=id[21]=-1,ad[149]=ad[21]=0,sd[149]=sd[21]=0,yd[86]=yd[84]=-1,vd[86]=vd[84]=0,_d[86]=_d[84]=1,bd[86]=bd[84]=0,wd[86]=wd[84]=-1,xd[86]=xd[84]=1,cd[89]=cd[81]=0,ud[89]=ud[81]=-1,ld[89]=ld[81]=0,hd[89]=hd[81]=1,gd[89]=gd[81]=0,md[89]=md[81]=1,cd[96]=cd[74]=0,ud[96]=ud[74]=1,ld[96]=ld[74]=0,dd[96]=dd[74]=-1,pd[96]=pd[74]=0,fd[96]=fd[74]=1,bd[96]=bd[74]=1,wd[96]=wd[74]=0,xd[96]=xd[74]=0,nd[96]=nd[74]=1,rd[96]=rd[74]=0,od[96]=od[74]=1,cd[24]=cd[146]=0,ud[24]=ud[146]=-1,ld[24]=ld[146]=1,yd[24]=yd[146]=1,vd[24]=vd[146]=0,_d[24]=_d[146]=1,hd[24]=hd[146]=0,gd[24]=gd[146]=1,md[24]=md[146]=1,id[24]=id[146]=0,ad[24]=ad[146]=-1,sd[24]=sd[146]=0,dd[6]=dd[164]=-1,pd[6]=pd[164]=0,fd[6]=fd[164]=1,yd[6]=yd[164]=-1,vd[6]=vd[164]=0,_d[6]=_d[164]=0,Ed[6]=Ed[164]=0,Cd[6]=Cd[164]=-1,Sd[6]=Sd[164]=1,bd[6]=bd[164]=1,wd[6]=wd[164]=0,xd[6]=xd[164]=0,hd[129]=hd[41]=0,gd[129]=gd[41]=1,md[129]=md[41]=1,Ed[129]=Ed[41]=0,Cd[129]=Cd[41]=1,Sd[129]=Sd[41]=0,nd[129]=nd[41]=-1,rd[129]=rd[41]=0,od[129]=od[41]=0,id[129]=id[41]=0,ad[129]=ad[41]=-1,sd[129]=sd[41]=0,yd[66]=yd[104]=0,vd[66]=vd[104]=1,_d[66]=_d[104]=0,hd[66]=hd[104]=-1,gd[66]=gd[104]=0,md[66]=md[104]=1,bd[66]=bd[104]=0,wd[66]=wd[104]=-1,xd[66]=xd[104]=0,nd[66]=nd[104]=0,rd[66]=rd[104]=-1,od[66]=od[104]=1,cd[144]=cd[26]=-1,ud[144]=ud[26]=0,ld[144]=ld[26]=0,Ed[144]=Ed[26]=1,Cd[144]=Cd[26]=0,Sd[144]=Sd[26]=1,bd[144]=bd[26]=0,wd[144]=wd[26]=1,xd[144]=xd[26]=1,id[144]=id[26]=-1,ad[144]=ad[26]=0,sd[144]=sd[26]=1,dd[36]=dd[134]=0,pd[36]=pd[134]=1,fd[36]=fd[134]=1,yd[36]=yd[134]=0,vd[36]=vd[134]=1,_d[36]=_d[134]=0,nd[36]=nd[134]=0,rd[36]=rd[134]=-1,od[36]=od[134]=1,id[36]=id[134]=1,ad[36]=ad[134]=0,sd[36]=sd[134]=0,cd[9]=cd[161]=-1,ud[9]=ud[161]=0,ld[9]=ld[161]=0,dd[9]=dd[161]=0,pd[9]=pd[161]=-1,fd[9]=fd[161]=0,hd[9]=hd[161]=1,gd[9]=gd[161]=0,md[9]=md[161]=0,Ed[9]=Ed[161]=1,Cd[9]=Cd[161]=0,Sd[9]=Sd[161]=1,cd[136]=0,ud[136]=1,ld[136]=1,dd[136]=0,pd[136]=1,fd[136]=0,yd[136]=-1,vd[136]=0,_d[136]=1,hd[136]=-1,gd[136]=0,md[136]=0,Ed[136]=0,Cd[136]=-1,Sd[136]=0,bd[136]=0,wd[136]=-1,xd[136]=1,nd[136]=1,rd[136]=0,od[136]=0,id[136]=1,ad[136]=0,sd[136]=1,cd[34]=0,ud[34]=-1,ld[34]=0,dd[34]=0,pd[34]=-1,fd[34]=1,yd[34]=1,vd[34]=0,_d[34]=0,hd[34]=1,gd[34]=0,md[34]=1,Ed[34]=0,Cd[34]=1,Sd[34]=1,bd[34]=0,wd[34]=1,xd[34]=0,nd[34]=-1,rd[34]=0,od[34]=1,id[34]=-1,ad[34]=0,sd[34]=0,cd[35]=0,ud[35]=1,ld[35]=1,dd[35]=0,pd[35]=-1,fd[35]=1,yd[35]=1,vd[35]=0,_d[35]=0,hd[35]=-1,gd[35]=0,md[35]=0,Ed[35]=0,Cd[35]=-1,Sd[35]=0,bd[35]=0,wd[35]=1,xd[35]=0,nd[35]=-1,rd[35]=0,od[35]=1,id[35]=1,ad[35]=0,sd[35]=1,cd[153]=0,ud[153]=1,ld[153]=1,hd[153]=-1;gd[153]=0,md[153]=0,Ed[153]=0,Cd[153]=-1,Sd[153]=0,id[153]=1,ad[153]=0,sd[153]=1,dd[102]=0,pd[102]=-1,fd[102]=1,yd[102]=1,vd[102]=0,_d[102]=0,bd[102]=0,wd[102]=1,xd[102]=0,nd[102]=-1,rd[102]=0,od[102]=1,cd[155]=0,ud[155]=-1,ld[155]=0,hd[155]=1,gd[155]=0,md[155]=1,Ed[155]=0,Cd[155]=1,Sd[155]=1,id[155]=-1,ad[155]=0,sd[155]=0,dd[103]=0,pd[103]=1,fd[103]=0,yd[103]=-1,vd[103]=0,_d[103]=1,bd[103]=0,wd[103]=-1,xd[103]=1,nd[103]=1,rd[103]=0,od[103]=0,cd[152]=0,ud[152]=1,ld[152]=1,yd[152]=-1,vd[152]=0,_d[152]=1,hd[152]=-1,gd[152]=0,md[152]=0,Ed[152]=0,Cd[152]=-1,Sd[152]=0,bd[152]=0,wd[152]=-1,xd[152]=1,id[152]=1,ad[152]=0,sd[152]=1,cd[156]=0,ud[156]=-1,ld[156]=1,yd[156]=1,vd[156]=0,_d[156]=1,hd[156]=-1,gd[156]=0,md[156]=0,Ed[156]=0,Cd[156]=-1,Sd[156]=0,bd[156]=0,wd[156]=1,xd[156]=1,id[156]=-1,ad[156]=0,sd[156]=1,cd[137]=0,ud[137]=1,ld[137]=1,dd[137]=0,pd[137]=1,fd[137]=0,hd[137]=-1,gd[137]=0,md[137]=0,Ed[137]=0,Cd[137]=-1,Sd[137]=0,nd[137]=1,rd[137]=0,od[137]=0,id[137]=1,ad[137]=0,sd[137]=1,cd[139]=0,ud[139]=1,ld[139]=1,dd[139]=0,pd[139]=-1,fd[139]=0,hd[139]=1,gd[139]=0,md[139]=0,Ed[139]=0,Cd[139]=1,Sd[139]=0,nd[139]=-1,rd[139]=0,od[139]=0,id[139]=1,ad[139]=0,sd[139]=1,cd[98]=0,ud[98]=-1,ld[98]=0,dd[98]=0,pd[98]=-1,fd[98]=1,yd[98]=1,vd[98]=0,_d[98]=0,hd[98]=1,gd[98]=0,md[98]=1,bd[98]=0,wd[98]=1,xd[98]=0,nd[98]=-1,rd[98]=0,od[98]=1,cd[99]=0,ud[99]=1,ld[99]=0,dd[99]=0,pd[99]=-1,fd[99]=1,yd[99]=1,vd[99]=0,_d[99]=0,hd[99]=-1,gd[99]=0,md[99]=1,bd[99]=0,wd[99]=-1,xd[99]=0,nd[99]=1,rd[99]=0,od[99]=1,dd[38]=0,pd[38]=-1,fd[38]=1,yd[38]=1,vd[38]=0,_d[38]=0,Ed[38]=0,Cd[38]=1,Sd[38]=1,bd[38]=0,wd[38]=1,xd[38]=0,nd[38]=-1,rd[38]=0,od[38]=1,id[38]=-1,ad[38]=0,sd[38]=0,dd[39]=0,pd[39]=1,fd[39]=1,yd[39]=-1,vd[39]=0,_d[39]=0,Ed[39]=0,Cd[39]=-1,Sd[39]=1,bd[39]=0,wd[39]=1,xd[39]=0,nd[39]=-1,rd[39]=0,od[39]=1,id[39]=1,ad[39]=0,sd[39]=0;var Id=function(t){return[[t.bottomleft,0],[0,0],[0,t.leftbottom]]},Pd=function(t){return[[1,t.rightbottom],[1,0],[t.bottomright,0]]},Ad=function(t){return[[t.topright,1],[1,1],[1,t.righttop]]},Nd=function(t){return[[0,t.lefttop],[0,1],[t.topleft,1]]},Md=function(t){return[[t.bottomright,0],[t.bottomleft,0],[0,t.leftbottom],[0,t.lefttop]]},Ld=function(t){return[[t.bottomright,0],[t.bottomleft,0],[1,t.righttop],[1,t.rightbottom]]},Dd=function(t){return[[1,t.righttop],[1,t.rightbottom],[t.topleft,1],[t.topright,1]]},Od=function(t){return[[0,t.leftbottom],[0,t.lefttop],[t.topleft,1],[t.topright,1]]},Td=function(t){return[[0,0],[0,t.leftbottom],[1,t.rightbottom],[1,0]]},Rd=function(t){return[[1,0],[t.bottomright,0],[t.topright,1],[1,1]]},Fd=function(t){return[[1,1],[1,t.righttop],[0,t.lefttop],[0,1]]},Gd=function(t){return[[t.bottomleft,0],[0,0],[0,1],[t.topleft,1]]},qd=function(t){return[[1,t.righttop],[1,t.rightbottom],[0,t.leftbottom],[0,t.lefttop]]},kd=function(t){return[[t.topleft,1],[t.topright,1],[t.bottomright,0],[t.bottomleft,0]]},Ud=function(){return[[0,0],[0,1],[1,1],[1,0]]},Bd=function(t){return[[1,t.rightbottom],[1,0],[0,0],[0,1],[t.topleft,1]]},Vd=function(t){return[[t.topright,1],[1,1],[1,0],[0,0],[0,t.leftbottom]]},zd=function(t){return[[1,0],[t.bottomright,0],[0,t.lefttop],[0,1],[1,1]]},jd=function(t){return[[1,1],[1,t.righttop],[t.bottomleft,0],[0,0],[0,1]]},Yd=function(t){return[[1,t.righttop],[1,t.rightbottom],[0,t.lefttop],[0,1],[t.topleft,1]]},Xd=function(t){return[[1,1],[1,t.righttop],[t.bottomright,0],[t.bottomleft,0],[t.topright,1]]},Wd=function(t){return[[1,t.rightbottom],[1,0],[t.bottomright,0],[0,t.leftbottom],[0,t.lefttop]]},$d=function(t){return[[t.topright,1],[t.bottomleft,0],[0,0],[0,t.leftbottom],[t.topleft,1]]},Hd=function(t){return[[t.bottomright,0],[t.bottomleft,0],[0,t.lefttop],[0,1],[t.topleft,1]]},Kd=function(t){return[[1,1],[1,t.righttop],[0,t.leftbottom],[0,t.lefttop],[t.topright,1]]},Jd=function(t){return[[1,t.rightbottom],[1,0],[t.bottomright,0],[t.topleft,1],[t.topright,1]]},Zd=function(t){return[[1,t.righttop],[1,t.rightbottom],[t.bottomleft,0],[0,0],[0,t.leftbottom]]},Qd=function(t){return[[1,t.rightbottom],[1,0],[0,0],[0,t.leftbottom],[t.topleft,1],[t.topright,1]]},tp=function(t){return[[1,1],[1,0],[t.bottomright,0],[0,t.leftbottom],[0,t.lefttop],[t.topright,1]]},ep=function(t){return[[1,1],[1,t.righttop],[t.bottomright,0],[t.bottomleft,0],[0,t.lefttop],[0,1]]},np=function(t){return[[1,t.righttop],[1,t.rightbottom],[t.bottomleft,0],[0,0],[0,1],[t.topleft,1]]},rp=function(t){return[[1,1],[1,t.righttop],[t.bottomleft,0],[0,0],[0,t.leftbottom],[t.topright,1]]},op=function(t){return[[1,t.rightbottom],[1,0],[t.bottomright,0],[0,t.lefttop],[0,1],[t.topleft,1]]},ip=function(t){return[[1,t.righttop],[1,t.rightbottom],[t.bottomright,0],[t.bottomleft,0],[0,t.leftbottom],[0,t.lefttop],[t.topleft,1],[t.topright,1]]},ap=function(t){return[[1,1],[1,t.righttop],[t.bottomleft,0],[0,0],[0,t.leftbottom],[t.topright,1]]},sp=function(t){return[[1,t.rightbottom],[1,0],[t.bottomright,0],[0,t.lefttop],[0,1],[t.topleft,1]]},cp=function(t){return[[1,1],[1,t.righttop],[t.bottomright,0],[t.bottomleft,0],[0,t.leftbottom],[0,t.lefttop],[t.topright,1]]},up=function(t){return[[1,t.righttop],[1,t.rightbottom],[t.bottomleft,0],[0,0],[0,t.leftbottom],[t.topleft,1],[t.topright,1]]},lp=function(t){return[[1,t.righttop],[1,t.rightbottom],[t.bottomright,0],[t.bottomleft,0],[0,t.lefttop],[0,1],[t.topleft,1]]},dp=function(t){return[[1,t.rightbottom],[1,0],[t.bottomright,0],[0,t.leftbottom],[0,t.lefttop],[t.topleft,1],[t.topright,1]]},pp=[],fp=[],hp=[],gp=[],mp=[],yp=[],vp=[],_p=[];gp[1]=mp[1]=18,gp[169]=mp[169]=18,hp[4]=fp[4]=12,hp[166]=fp[166]=12,pp[16]=_p[16]=4,pp[154]=_p[154]=4,yp[64]=vp[64]=22,yp[106]=vp[106]=22,hp[2]=yp[2]=17,gp[2]=mp[2]=18,hp[168]=yp[168]=17,gp[168]=mp[168]=18,pp[8]=gp[8]=9,fp[8]=hp[8]=12,pp[162]=gp[162]=9,fp[162]=hp[162]=12,pp[32]=_p[32]=4,fp[32]=vp[32]=1,pp[138]=_p[138]=4,fp[138]=vp[138]=1,mp[128]=_p[128]=21,yp[128]=vp[128]=22,mp[42]=_p[42]=21,yp[42]=vp[42]=22,fp[5]=mp[5]=14,fp[165]=mp[165]=14,hp[20]=_p[20]=6,hp[150]=_p[150]=6,pp[80]=yp[80]=11,pp[90]=yp[90]=11,gp[65]=vp[65]=3,gp[105]=vp[105]=3,pp[160]=yp[160]=11,fp[160]=mp[160]=14,pp[10]=yp[10]=11,fp[10]=mp[10]=14,hp[130]=_p[130]=6,gp[130]=vp[130]=3,hp[40]=_p[40]=6,gp[40]=vp[40]=3,fp[101]=vp[101]=1,fp[69]=vp[69]=1,mp[149]=_p[149]=21,mp[21]=_p[21]=21,hp[86]=yp[86]=17,hp[84]=yp[84]=17,pp[89]=gp[89]=9,pp[81]=gp[81]=9,pp[96]=vp[96]=0,fp[96]=yp[96]=15,pp[74]=vp[74]=0,fp[74]=yp[74]=15,pp[24]=hp[24]=8,gp[24]=_p[24]=7,pp[146]=hp[146]=8,gp[146]=_p[146]=7,fp[6]=yp[6]=15,hp[6]=mp[6]=16,fp[164]=yp[164]=15,hp[164]=mp[164]=16,gp[129]=_p[129]=7,mp[129]=vp[129]=20,gp[41]=_p[41]=7,mp[41]=vp[41]=20,hp[66]=vp[66]=2,gp[66]=yp[66]=19,hp[104]=vp[104]=2,gp[104]=yp[104]=19,pp[144]=mp[144]=10,yp[144]=_p[144]=23,pp[26]=mp[26]=10,yp[26]=_p[26]=23,fp[36]=_p[36]=5,hp[36]=vp[36]=2,fp[134]=_p[134]=5,hp[134]=vp[134]=2,pp[9]=mp[9]=10,fp[9]=gp[9]=13,pp[161]=mp[161]=10,fp[161]=gp[161]=13,fp[37]=_p[37]=5,mp[37]=vp[37]=20,fp[133]=_p[133]=5,mp[133]=vp[133]=20,hp[148]=mp[148]=16,yp[148]=_p[148]=23,hp[22]=mp[22]=16,yp[22]=_p[22]=23,pp[82]=hp[82]=8,gp[82]=yp[82]=19,pp[88]=hp[88]=8,gp[88]=yp[88]=19,pp[73]=vp[73]=0,fp[73]=gp[73]=13,pp[97]=vp[97]=0,fp[97]=gp[97]=13,pp[145]=gp[145]=9,mp[145]=_p[145]=21,pp[25]=gp[25]=9,mp[25]=_p[25]=21,fp[70]=vp[70]=1,hp[70]=yp[70]=17,fp[100]=vp[100]=1,hp[100]=yp[100]=17,pp[34]=gp[34]=9,fp[34]=hp[34]=12,mp[34]=_p[34]=21,yp[34]=vp[34]=22,pp[136]=_p[136]=4,fp[136]=vp[136]=1,hp[136]=yp[136]=17,gp[136]=mp[136]=18,pp[35]=_p[35]=4,fp[35]=hp[35]=12,gp[35]=mp[35]=18,yp[35]=vp[35]=22,pp[153]=_p[153]=4,gp[153]=mp[153]=18,fp[102]=hp[102]=12,yp[102]=vp[102]=22,pp[155]=gp[155]=9,mp[155]=_p[155]=23,fp[103]=vp[103]=1,hp[103]=yp[103]=17,pp[152]=_p[152]=4,hp[152]=yp[152]=17,gp[152]=mp[152]=18,pp[156]=hp[156]=8,gp[156]=mp[156]=18,yp[156]=_p[156]=23,pp[137]=_p[137]=4,fp[137]=vp[137]=1,gp[137]=mp[137]=18,pp[139]=_p[139]=4,fp[139]=gp[139]=13,mp[139]=vp[139]=20,pp[98]=gp[98]=9,fp[98]=hp[98]=12,yp[98]=vp[98]=22,pp[99]=vp[99]=0,fp[99]=hp[99]=12,gp[99]=yp[99]=19,fp[38]=hp[38]=12,mp[38]=_p[38]=21,yp[38]=vp[38]=22,fp[39]=_p[39]=5,hp[39]=mp[39]=16,yp[39]=vp[39]=22;var bp=[];bp[1]=bp[169]=Id,bp[4]=bp[166]=Pd,bp[16]=bp[154]=Ad,bp[64]=bp[106]=Nd,bp[168]=bp[2]=Md,bp[162]=bp[8]=Ld,bp[138]=bp[32]=Dd,bp[42]=bp[128]=Od,bp[5]=bp[165]=Td,bp[20]=bp[150]=Rd,bp[80]=bp[90]=Fd,bp[65]=bp[105]=Gd,bp[160]=bp[10]=qd,bp[130]=bp[40]=kd,bp[85]=Ud,bp[101]=bp[69]=Bd,bp[149]=bp[21]=Vd,bp[86]=bp[84]=zd,bp[89]=bp[81]=jd,bp[96]=bp[74]=Yd,bp[24]=bp[146]=Xd,bp[6]=bp[164]=Wd,bp[129]=bp[41]=$d,bp[66]=bp[104]=Hd,bp[144]=bp[26]=Kd,bp[36]=bp[134]=Jd,bp[9]=bp[161]=Zd,bp[37]=bp[133]=Qd,bp[148]=bp[22]=tp,bp[82]=bp[88]=ep,bp[73]=bp[97]=np,bp[145]=bp[25]=rp,bp[70]=bp[100]=op,bp[34]=function(t){return[Od(t),Ld(t)]},bp[35]=ip,bp[136]=function(t){return[Dd(t),Md(t)]},bp[153]=function(t){return[Ad(t),Id(t)]},bp[102]=function(t){return[Pd(t),Nd(t)]},bp[155]=ap,bp[103]=sp,bp[152]=function(t){return[Ad(t),Md(t)]},bp[156]=cp,bp[137]=function(t){return[Dd(t),Id(t)]},bp[139]=up,bp[98]=function(t){return[Ld(t),Nd(t)]},bp[99]=lp,bp[38]=function(t){return[Pd(t),Od(t)]},bp[39]=dp;var wp=function t(e){this.id=t.buildId(e),this.coordinates=e,this.innerEdges=[],this.outerEdges=[],this.outerEdgesSorted=!1};wp.buildId=function(t){return t.join(",")},wp.prototype.removeInnerEdge=function(t){this.innerEdges=this.innerEdges.filter(function(e){return e.from.id!==t.from.id})},wp.prototype.removeOuterEdge=function(t){this.outerEdges=this.outerEdges.filter(function(e){return e.to.id!==t.to.id})},wp.prototype.addOuterEdge=function(t){this.outerEdges.push(t),this.outerEdgesSorted=!1},wp.prototype.sortOuterEdges=function(){var t=this;this.outerEdgesSorted||(this.outerEdges.sort(function(e,n){var r=e.to,o=n.to;if(r.coordinates[0]-t.coordinates[0]>=0&&o.coordinates[0]-t.coordinates[0]<0)return 1;if(r.coordinates[0]-t.coordinates[0]<0&&o.coordinates[0]-t.coordinates[0]>=0)return-1;if(r.coordinates[0]-t.coordinates[0]==0&&o.coordinates[0]-t.coordinates[0]==0)return r.coordinates[1]-t.coordinates[1]>=0||o.coordinates[1]-t.coordinates[1]>=0?r.coordinates[1]-o.coordinates[1]:o.coordinates[1]-r.coordinates[1];var i=aa(t.coordinates,r.coordinates,o.coordinates);return i<0?1:i>0?-1:Math.pow(r.coordinates[0]-t.coordinates[0],2)+Math.pow(r.coordinates[1]-t.coordinates[1],2)-(Math.pow(o.coordinates[0]-t.coordinates[0],2)+Math.pow(o.coordinates[1]-t.coordinates[1],2))}),this.outerEdgesSorted=!0)},wp.prototype.getOuterEdges=function(){return this.sortOuterEdges(),this.outerEdges},wp.prototype.getOuterEdge=function(t){return this.sortOuterEdges(),this.outerEdges[t]},wp.prototype.addInnerEdge=function(t){this.innerEdges.push(t)};var xp=function(t,e){this.from=t,this.to=e,this.next=void 0,this.label=void 0,this.symetric=void 0,this.ring=void 0,this.from.addOuterEdge(this),this.to.addInnerEdge(this)};xp.prototype.getSymetric=function(){return this.symetric||(this.symetric=new xp(this.to,this.from),this.symetric.symetric=this),this.symetric},xp.prototype.deleteEdge=function(){this.from.removeOuterEdge(this),this.to.removeInnerEdge(this)},xp.prototype.isEqual=function(t){return this.from.id===t.from.id&&this.to.id===t.to.id},xp.prototype.toString=function(){return"Edge { "+this.from.id+" -> "+this.to.id+" }"},xp.prototype.toLineString=function(){return s([this.from.coordinates,this.to.coordinates])},xp.prototype.compareTo=function(t){return aa(t.from.coordinates,t.to.coordinates,this.to.coordinates)};var Ep=function(){this.edges=[],this.polygon=void 0,this.envelope=void 0},Cp={length:{configurable:!0}};Ep.prototype.push=function(t){this[this.edges.length]=t,this.edges.push(t),this.polygon=this.envelope=void 0},Ep.prototype.get=function(t){return this.edges[t]},Cp.length.get=function(){return this.edges.length},Ep.prototype.forEach=function(t){this.edges.forEach(t)},Ep.prototype.map=function(t){return this.edges.map(t)},Ep.prototype.some=function(t){return this.edges.some(t)},Ep.prototype.isValid=function(){return!0},Ep.prototype.isHole=function(){var t=this,e=this.edges.reduce(function(e,n,r){return n.from.coordinates[1]>t.edges[e].from.coordinates[1]&&(e=r),e},0),n=(0===e?this.length:e)-1,r=(e+1)%this.length,o=aa(this.edges[n].from.coordinates,this.edges[e].from.coordinates,this.edges[r].from.coordinates);return 0===o?this.edges[n].from.coordinates[0]>this.edges[r].from.coordinates[0]:o>0},Ep.prototype.toMultiPoint=function(){return d(this.edges.map(function(t){return t.from.coordinates}))},Ep.prototype.toPolygon=function(){if(this.polygon)return this.polygon;var t=this.edges.map(function(t){return t.from.coordinates});return t.push(this.edges[0].from.coordinates),this.polygon=i([t])},Ep.prototype.getEnvelope=function(){return this.envelope?this.envelope:this.envelope=pn(this.toPolygon())},Ep.findEdgeRingContaining=function(t,e){var n,o,i=t.getEnvelope();return e.forEach(function(e){var a=e.getEnvelope();if(o&&(n=o.getEnvelope()),!sa(a,i)&&ca(a,i)){var s=t.map(function(t){return t.from.coordinates}).find(function(t){return!e.some(function(e){return ua(t,e.from.coordinates)})});s&&e.inside(r(s))&&(o&&!ca(n,a)||(o=e))}}),o},Ep.prototype.inside=function(t){return ce(t,this.toPolygon())},Object.defineProperties(Ep.prototype,Cp);var Sp=function(){this.edges=[],this.nodes={}};Sp.fromGeoJson=function(t){la(t);var e=new Sp;return z(t,function(t){tt(t,"LineString","Graph::fromGeoJson"),R(t,function(t,n){if(t){var r=e.getNode(t),o=e.getNode(n);e.addEdge(r,o)}return n})}),e},Sp.prototype.getNode=function(t){var e=wp.buildId(t),n=this.nodes[e];return n||(n=this.nodes[e]=new wp(t)),n},Sp.prototype.addEdge=function(t,e){var n=new xp(t,e),r=n.getSymetric();this.edges.push(n),this.edges.push(r)},Sp.prototype.deleteDangles=function(){var t=this;Object.keys(this.nodes).map(function(e){return t.nodes[e]}).forEach(function(e){return t._removeIfDangle(e)})},Sp.prototype._removeIfDangle=function(t){var e=this;if(t.innerEdges.length<=1){var n=t.getOuterEdges().map(function(t){return t.to});this.removeNode(t),n.forEach(function(t){return e._removeIfDangle(t)})}},Sp.prototype.deleteCutEdges=function(){var t=this;this._computeNextCWEdges(),this._findLabeledEdgeRings(),this.edges.forEach(function(e){e.label===e.symetric.label&&(t.removeEdge(e.symetric),t.removeEdge(e))})},Sp.prototype._computeNextCWEdges=function(t){var e=this;void 0===t?Object.keys(this.nodes).forEach(function(t){return e._computeNextCWEdges(e.nodes[t])}):t.getOuterEdges().forEach(function(e,n){t.getOuterEdge((0===n?t.getOuterEdges().length:n)-1).symetric.next=e})},Sp.prototype._computeNextCCWEdges=function(t,e){for(var n,r,o=t.getOuterEdges(),i=o.length-1;i>=0;--i){var a=o[i],s=a.symetric,c=void 0,u=void 0;a.label===e&&(c=a),s.label===e&&(u=s),c&&u&&(u&&(r=u),c&&(r&&(r.next=c,r=void 0),n||(n=c)))}r&&(r.next=n)},Sp.prototype._findLabeledEdgeRings=function(){var t=[],e=0;return this.edges.forEach(function(n){if(!(n.label>=0)){t.push(n);var r=n;do{r.label=e,r=r.next}while(!n.isEqual(r));e++}}),t},Sp.prototype.getEdgeRings=function(){var t=this;this._computeNextCWEdges(),this.edges.forEach(function(t){t.label=void 0}),this._findLabeledEdgeRings().forEach(function(e){t._findIntersectionNodes(e).forEach(function(n){t._computeNextCCWEdges(n,e.label)})});var e=[];return this.edges.forEach(function(n){
n.ring||e.push(t._findEdgeRing(n))}),e},Sp.prototype._findIntersectionNodes=function(t){var e=[],n=t;do{!function(){var r=0;n.from.getOuterEdges().forEach(function(e){e.label===t.label&&++r}),r>1&&e.push(n.from),n=n.next}()}while(!t.isEqual(n));return e},Sp.prototype._findEdgeRing=function(t){var e=t,n=new Ep;do{n.push(e),e.ring=n,e=e.next}while(!t.isEqual(e));return n},Sp.prototype.removeNode=function(t){var e=this;t.getOuterEdges().forEach(function(t){return e.removeEdge(t)}),t.innerEdges.forEach(function(t){return e.removeEdge(t)}),delete this.nodes[t.id]},Sp.prototype.removeEdge=function(t){this.edges=this.edges.filter(function(e){return!e.isEqual(t)}),t.deleteEdge()};var Ip=Gt(function(t,e){function n(t){var e=[];for(var n in t)e.push(n);return e}e=t.exports="function"==typeof Object.keys?Object.keys:n,e.shim=n}),Pp=(Ip.shim,Gt(function(t,e){function n(t){return"[object Arguments]"==Object.prototype.toString.call(t)}function r(t){return t&&"object"==typeof t&&"number"==typeof t.length&&Object.prototype.hasOwnProperty.call(t,"callee")&&!Object.prototype.propertyIsEnumerable.call(t,"callee")||!1}var o="[object Arguments]"==function(){return Object.prototype.toString.call(arguments)}();e=t.exports=o?n:r,e.supported=n,e.unsupported=r})),Ap=(Pp.supported,Pp.unsupported,Gt(function(t){function e(t){return null===t||void 0===t}function n(t){return!(!t||"object"!=typeof t||"number"!=typeof t.length)&&("function"==typeof t.copy&&"function"==typeof t.slice&&!(t.length>0&&"number"!=typeof t[0]))}function r(t,r,a){var s,c;if(e(t)||e(r))return!1;if(t.prototype!==r.prototype)return!1;if(Pp(t))return!!Pp(r)&&(t=o.call(t),r=o.call(r),i(t,r,a));if(n(t)){if(!n(r))return!1;if(t.length!==r.length)return!1;for(s=0;s<t.length;s++)if(t[s]!==r[s])return!1;return!0}try{var u=Ip(t),l=Ip(r)}catch(t){return!1}if(u.length!=l.length)return!1;for(u.sort(),l.sort(),s=u.length-1;s>=0;s--)if(u[s]!=l[s])return!1;for(s=u.length-1;s>=0;s--)if(c=u[s],!i(t[c],r[c],a))return!1;return typeof t==typeof r}var o=Array.prototype.slice,i=t.exports=function(t,e,n){return n||(n={}),t===e||(t instanceof Date&&e instanceof Date?t.getTime()===e.getTime():!t||!e||"object"!=typeof t&&"object"!=typeof e?n.strict?t===e:t==e:r(t,e,n))}})),Np=function(t){this.precision=t&&t.precision?t.precision:17,this.direction=!(!t||!t.direction)&&t.direction,this.pseudoNode=!(!t||!t.pseudoNode)&&t.pseudoNode,this.objectComparator=t&&t.objectComparator?t.objectComparator:ka};Np.prototype.compare=function(t,e){if(t.type!==e.type||!qa(t,e))return!1;switch(t.type){case"Point":return this.compareCoord(t.coordinates,e.coordinates);case"LineString":return this.compareLine(t.coordinates,e.coordinates,0,!1);case"Polygon":return this.comparePolygon(t,e);case"Feature":return this.compareFeature(t,e);default:if(0===t.type.indexOf("Multi")){var n=this,r=Ga(t),o=Ga(e);return r.every(function(t){return this.some(function(e){return n.compare(t,e)})},o)}}return!1},Np.prototype.compareCoord=function(t,e){if(t.length!==e.length)return!1;for(var n=0;n<t.length;n++)if(t[n].toFixed(this.precision)!==e[n].toFixed(this.precision))return!1;return!0},Np.prototype.compareLine=function(t,e,n,r){if(!qa(t,e))return!1;var o=this.pseudoNode?t:this.removePseudo(t),i=this.pseudoNode?e:this.removePseudo(e);if(!r||this.compareCoord(o[0],i[0])||(i=this.fixStartIndex(i,o))){var a=this.compareCoord(o[n],i[n]);return this.direction||a?this.comparePath(o,i):!!this.compareCoord(o[n],i[i.length-(1+n)])&&this.comparePath(o.slice().reverse(),i)}},Np.prototype.fixStartIndex=function(t,e){for(var n,r=-1,o=0;o<t.length;o++)if(this.compareCoord(t[o],e[0])){r=o;break}return r>=0&&(n=[].concat(t.slice(r,t.length),t.slice(1,r+1))),n},Np.prototype.comparePath=function(t,e){var n=this;return t.every(function(t,e){return n.compareCoord(t,this[e])},e)},Np.prototype.comparePolygon=function(t,e){if(this.compareLine(t.coordinates[0],e.coordinates[0],1,!0)){var n=t.coordinates.slice(1,t.coordinates.length),r=e.coordinates.slice(1,e.coordinates.length),o=this;return n.every(function(t){return this.some(function(e){return o.compareLine(t,e,1,!0)})},r)}return!1},Np.prototype.compareFeature=function(t,e){return!(t.id!==e.id||!this.objectComparator(t.properties,e.properties)||!this.compareBBox(t,e))&&this.compare(t.geometry,e.geometry)},Np.prototype.compareBBox=function(t,e){return!!(!t.bbox&&!e.bbox||t.bbox&&e.bbox&&this.compareCoord(t.bbox,e.bbox))},Np.prototype.removePseudo=function(t){return t};var Mp=Np,Lp=Gt(function(t){/**
 * DBSCAN - Density based clustering
 *
 * @author Lukasz Krawczyk <contact@lukaszkrawczyk.eu>
 * @copyright MIT
 */
function e(t,e,n,r){this.dataset=[],this.epsilon=1,this.minPts=2,this.distance=this._euclideanDistance,this.clusters=[],this.noise=[],this._visited=[],this._assigned=[],this._datasetLength=0,this._init(t,e,n,r)}e.prototype.run=function(t,e,n,r){this._init(t,e,n,r);for(var o=0;o<this._datasetLength;o++)if(1!==this._visited[o]){this._visited[o]=1;var i=this._regionQuery(o);if(i.length<this.minPts)this.noise.push(o);else{var a=this.clusters.length;this.clusters.push([]),this._addToCluster(o,a),this._expandCluster(a,i)}}return this.clusters},e.prototype._init=function(t,e,n,r){if(t){if(!(t instanceof Array))throw Error("Dataset must be of type array, "+typeof t+" given");this.dataset=t,this.clusters=[],this.noise=[],this._datasetLength=t.length,this._visited=new Array(this._datasetLength),this._assigned=new Array(this._datasetLength)}e&&(this.epsilon=e),n&&(this.minPts=n),r&&(this.distance=r)},e.prototype._expandCluster=function(t,e){for(var n=0;n<e.length;n++){var r=e[n];if(1!==this._visited[r]){this._visited[r]=1;var o=this._regionQuery(r);o.length>=this.minPts&&(e=this._mergeArrays(e,o))}1!==this._assigned[r]&&this._addToCluster(r,t)}},e.prototype._addToCluster=function(t,e){this.clusters[e].push(t),this._assigned[t]=1},e.prototype._regionQuery=function(t){for(var e=[],n=0;n<this._datasetLength;n++){this.distance(this.dataset[t],this.dataset[n])<this.epsilon&&e.push(n)}return e},e.prototype._mergeArrays=function(t,e){for(var n=e.length,r=0;r<n;r++){var o=e[r];t.indexOf(o)<0&&t.push(o)}return t},e.prototype._euclideanDistance=function(t,e){for(var n=0,r=Math.min(t.length,e.length);r--;)n+=(t[r]-e[r])*(t[r]-e[r]);return Math.sqrt(n)},t.exports&&(t.exports=e)}),Dp=Gt(function(t){/**
 * KMEANS clustering
 *
 * @author Lukasz Krawczyk <contact@lukaszkrawczyk.eu>
 * @copyright MIT
 */
function e(t,e,n){this.k=3,this.dataset=[],this.assignments=[],this.centroids=[],this.init(t,e,n)}e.prototype.init=function(t,e,n){this.assignments=[],this.centroids=[],void 0!==t&&(this.dataset=t),void 0!==e&&(this.k=e),void 0!==n&&(this.distance=n)},e.prototype.run=function(t,e){this.init(t,e);for(var n=this.dataset.length,r=0;r<this.k;r++)this.centroids[r]=this.randomCentroid();for(var o=!0;o;){o=this.assign();for(var i=0;i<this.k;i++){for(var a=new Array(l),s=0,c=0;c<l;c++)a[c]=0;for(var u=0;u<n;u++){var l=this.dataset[u].length;if(i===this.assignments[u]){for(var c=0;c<l;c++)a[c]+=this.dataset[u][c];s++}}if(s>0){for(var c=0;c<l;c++)a[c]/=s;this.centroids[i]=a}else this.centroids[i]=this.randomCentroid(),o=!0}}return this.getClusters()},e.prototype.randomCentroid=function(){var t,e,n=this.dataset.length-1;do{e=Math.round(Math.random()*n),t=this.dataset[e]}while(this.centroids.indexOf(t)>=0);return t},e.prototype.assign=function(){for(var t,e=!1,n=this.dataset.length,r=0;r<n;r++)(t=this.argmin(this.dataset[r],this.centroids,this.distance))!=this.assignments[r]&&(this.assignments[r]=t,e=!0);return e},e.prototype.getClusters=function(){for(var t,e=new Array(this.k),n=0;n<this.assignments.length;n++)t=this.assignments[n],void 0===e[t]&&(e[t]=[]),e[t].push(n);return e},e.prototype.argmin=function(t,e,n){for(var r,o=Number.MAX_VALUE,i=0,a=e.length,s=0;s<a;s++)(r=n(t,e[s]))<o&&(o=r,i=s);return i},e.prototype.distance=function(t,e){for(var n=0,r=Math.min(t.length,e.length);r--;){var o=t[r]-e[r];n+=o*o}return Math.sqrt(n)},t.exports&&(t.exports=e)}),Op=Gt(function(t){/**
 * PriorityQueue
 * Elements in this queue are sorted according to their value
 *
 * @author Lukasz Krawczyk <contact@lukaszkrawczyk.eu>
 * @copyright MIT
 */
function e(t,e,n){this._queue=[],this._priorities=[],this._sorting="desc",this._init(t,e,n)}e.prototype.insert=function(t,e){for(var n=this._queue.length,r=n;r--;){var o=this._priorities[r];"desc"===this._sorting?e>o&&(n=r):e<o&&(n=r)}this._insertAt(t,e,n)},e.prototype.remove=function(t){for(var e=this._queue.length;e--;){if(t===this._queue[e]){this._queue.splice(e,1),this._priorities.splice(e,1);break}}},e.prototype.forEach=function(t){this._queue.forEach(t)},e.prototype.getElements=function(){return this._queue},e.prototype.getElementPriority=function(t){return this._priorities[t]},e.prototype.getPriorities=function(){return this._priorities},e.prototype.getElementsWithPriorities=function(){for(var t=[],e=0,n=this._queue.length;e<n;e++)t.push([this._queue[e],this._priorities[e]]);return t},e.prototype._init=function(t,e,n){if(t&&e){if(this._queue=[],this._priorities=[],t.length!==e.length)throw new Error("Arrays must have the same length");for(var r=0;r<t.length;r++)this.insert(t[r],e[r])}n&&(this._sorting=n)},e.prototype._insertAt=function(t,e,n){this._queue.length===n?(this._queue.push(t),this._priorities.push(e)):(this._queue.splice(n,0,t),this._priorities.splice(n,0,e))},t.exports&&(t.exports=e)}),Tp=Gt(function(t){/**
 * OPTICS - Ordering points to identify the clustering structure
 *
 * @author Lukasz Krawczyk <contact@lukaszkrawczyk.eu>
 * @copyright MIT
 */
function e(t,e,n,r){this.epsilon=1,this.minPts=1,this.distance=this._euclideanDistance,this._reachability=[],this._processed=[],this._coreDistance=0,this._orderedList=[],this._init(t,e,n,r)}if(t.exports)var n=Op;e.prototype.run=function(t,e,r,o){this._init(t,e,r,o);for(var i=0,a=this.dataset.length;i<a;i++)if(1!==this._processed[i]){this._processed[i]=1,this.clusters.push([i]);var s=this.clusters.length-1;this._orderedList.push(i);var c=new n(null,null,"asc"),u=this._regionQuery(i);void 0!==this._distanceToCore(i)&&(this._updateQueue(i,u,c),this._expandCluster(s,c))}return this.clusters},e.prototype.getReachabilityPlot=function(){for(var t=[],e=0,n=this._orderedList.length;e<n;e++){var r=this._orderedList[e],o=this._reachability[r];t.push([r,o])}return t},e.prototype._init=function(t,e,n,r){if(t){if(!(t instanceof Array))throw Error("Dataset must be of type array, "+typeof t+" given");this.dataset=t,this.clusters=[],this._reachability=new Array(this.dataset.length),this._processed=new Array(this.dataset.length),this._coreDistance=0,this._orderedList=[]}e&&(this.epsilon=e),n&&(this.minPts=n),r&&(this.distance=r)},e.prototype._updateQueue=function(t,e,n){var r=this;this._coreDistance=this._distanceToCore(t),e.forEach(function(e){if(void 0===r._processed[e]){var o=r.distance(r.dataset[t],r.dataset[e]),i=Math.max(r._coreDistance,o);void 0===r._reachability[e]?(r._reachability[e]=i,n.insert(e,i)):i<r._reachability[e]&&(r._reachability[e]=i,n.remove(e),n.insert(e,i))}})},e.prototype._expandCluster=function(t,e){for(var n=e.getElements(),r=0,o=n.length;r<o;r++){var i=n[r];if(void 0===this._processed[i]){var a=this._regionQuery(i);this._processed[i]=1,this.clusters[t].push(i),this._orderedList.push(i),void 0!==this._distanceToCore(i)&&(this._updateQueue(i,a,e),this._expandCluster(t,e))}}},e.prototype._distanceToCore=function(t){for(var e=this.epsilon,n=0;n<e;n++){if(this._regionQuery(t,n).length>=this.minPts)return n}},e.prototype._regionQuery=function(t,e){e=e||this.epsilon;for(var n=[],r=0,o=this.dataset.length;r<o;r++)this.distance(this.dataset[t],this.dataset[r])<e&&n.push(r);return n},e.prototype._euclideanDistance=function(t,e){for(var n=0,r=Math.min(t.length,e.length);r--;)n+=(t[r]-e[r])*(t[r]-e[r]);return Math.sqrt(n)},t.exports&&(t.exports=e)}),Rp=Gt(function(t){t.exports&&(t.exports={DBSCAN:Lp,KMEANS:Dp,OPTICS:Tp,PriorityQueue:Op})}),Fp=(Rp.DBSCAN,Rp.KMEANS,Rp.OPTICS,Rp.PriorityQueue,{eudist:function(t,e,n){for(var r=t.length,o=0,i=0;i<r;i++){var a=(t[i]||0)-(e[i]||0);o+=a*a}return n?Math.sqrt(o):o},mandist:function(t,e,n){for(var r=t.length,o=0,i=0;i<r;i++)o+=Math.abs((t[i]||0)-(e[i]||0));return n?Math.sqrt(o):o},dist:function(t,e,n){var r=Math.abs(t-e);return n?r:r*r}}),Gp=Fp.eudist,qp=Fp.dist,kp={kmrand:function(t,e){for(var n={},r=[],o=e<<2,i=t.length,a=t[0].length>0;r.length<e&&o-- >0;){var s=t[Math.floor(Math.random()*i)],c=a?s.join("_"):""+s;n[c]||(n[c]=!0,r.push(s))}if(r.length<e)throw new Error("Error initializating clusters");return r},kmpp:function(t,e){var n=t[0].length?Gp:qp,r=[],o=t.length,i=t[0].length>0,a=t[Math.floor(Math.random()*o)];i&&a.join("_");for(r.push(a);r.length<e;){for(var s=[],c=r.length,u=0,l=[],d=0;d<o;d++){for(var p=1/0,f=0;f<c;f++){var h=n(t[d],r[f]);h<=p&&(p=h)}s[d]=p}for(var g=0;g<o;g++)u+=s[g];for(var m=0;m<o;m++)l[m]={i:m,v:t[m],pr:s[m]/u,cs:0};l.sort(function(t,e){return t.pr-e.pr}),l[0].cs=l[0].pr;for(var y=1;y<o;y++)l[y].cs=l[y-1].cs+l[y].pr;for(var v=Math.random(),_=0;_<o-1&&l[_++].cs<v;);r.push(l[_-1].v)}return r}},Up=Fp.eudist,Bp=kp.kmrand,Vp=kp.kmpp,zp=1e4,jp=ja,Yp={search:function(t,e,n,r){t.cleanDirty(),r=r||{};var o=r.heuristic||Yp.heuristics.manhattan,i=r.closest||!1,a=Ka(),s=e;for(e.h=o(e,n),a.push(e);a.size()>0;){var c=a.pop();if(c===n)return Ha(c);c.closed=!0;for(var u=t.neighbors(c),l=0,d=u.length;l<d;++l){var p=u[l];if(!p.closed&&!p.isWall()){var f=c.g+p.getCost(c),h=p.visited;(!h||f<p.g)&&(p.visited=!0,p.parent=c,p.h=p.h||o(p,n),p.g=f,p.f=p.g+p.h,t.markDirty(p),i&&(p.h<s.h||p.h===s.h&&p.g<s.g)&&(s=p),h?a.rescoreElement(p):a.push(p))}}}return i?Ha(s):[]},heuristics:{manhattan:function(t,e){return Math.abs(e.x-t.x)+Math.abs(e.y-t.y)},diagonal:function(t,e){var n=Math.sqrt(2),r=Math.abs(e.x-t.x),o=Math.abs(e.y-t.y);return 1*(r+o)+(n-2)*Math.min(r,o)}},cleanNode:function(t){t.f=0,t.g=0,t.h=0,t.visited=!1,t.closed=!1,t.parent=null}};Ja.prototype.init=function(){this.dirtyNodes=[];for(var t=0;t<this.nodes.length;t++)Yp.cleanNode(this.nodes[t])},Ja.prototype.cleanDirty=function(){for(var t=0;t<this.dirtyNodes.length;t++)Yp.cleanNode(this.dirtyNodes[t]);this.dirtyNodes=[]},Ja.prototype.markDirty=function(t){this.dirtyNodes.push(t)},Ja.prototype.neighbors=function(t){var e=[],n=t.x,r=t.y,o=this.grid;return o[n-1]&&o[n-1][r]&&e.push(o[n-1][r]),o[n+1]&&o[n+1][r]&&e.push(o[n+1][r]),o[n]&&o[n][r-1]&&e.push(o[n][r-1]),o[n]&&o[n][r+1]&&e.push(o[n][r+1]),this.diagonal&&(o[n-1]&&o[n-1][r-1]&&e.push(o[n-1][r-1]),o[n+1]&&o[n+1][r-1]&&e.push(o[n+1][r-1]),o[n-1]&&o[n-1][r+1]&&e.push(o[n-1][r+1]),o[n+1]&&o[n+1][r+1]&&e.push(o[n+1][r+1])),e},Ja.prototype.toString=function(){for(var t,e,n,r,o=[],i=this.grid,a=0,s=i.length;a<s;a++){for(t=[],e=i[a],n=0,r=e.length;n<r;n++)t.push(e[n].weight);o.push(t.join(" "))}return o.join("\n")},Za.prototype.toString=function(){return"["+this.x+" "+this.y+"]"},Za.prototype.getCost=function(t){return t&&t.x!==this.x&&t.y!==this.y?1.41421*this.weight:this.weight},Za.prototype.isWall=function(){return 0===this.weight},Qa.prototype={push:function(t){this.content.push(t),this.sinkDown(this.content.length-1)},pop:function(){var t=this.content[0],e=this.content.pop();return this.content.length>0&&(this.content[0]=e,this.bubbleUp(0)),t},remove:function(t){var e=this.content.indexOf(t),n=this.content.pop();e!==this.content.length-1&&(this.content[e]=n,this.scoreFunction(n)<this.scoreFunction(t)?this.sinkDown(e):this.bubbleUp(e))},size:function(){return this.content.length},rescoreElement:function(t){this.sinkDown(this.content.indexOf(t))},sinkDown:function(t){for(var e=this.content[t];t>0;){var n=(t+1>>1)-1,r=this.content[n];if(!(this.scoreFunction(e)<this.scoreFunction(r)))break;this.content[n]=e,this.content[t]=r,t=n}},bubbleUp:function(t){for(var e=this.content.length,n=this.content[t],r=this.scoreFunction(n);;){var o,i=t+1<<1,a=i-1,s=null;if(a<e){var c=this.content[a];o=this.scoreFunction(c),o<r&&(s=a)}if(i<e){var u=this.content[i];this.scoreFunction(u)<(null===s?r:o)&&(s=i)}if(null===s)break;this.content[t]=this.content[s],this.content[s]=n,t=s}}};var Xp=function(t){return function(){return t}};os.prototype={constructor:os,insert:function(t,e){var n,r,o;if(t){if(e.P=t,e.N=t.N,t.N&&(t.N.P=e),t.N=e,t.R){for(t=t.R;t.L;)t=t.L;t.L=e}else t.R=e;n=t}else this._?(t=cs(this._),e.P=null,e.N=t,t.P=t.L=e,n=t):(e.P=e.N=null,this._=e,n=null);for(e.L=e.R=null,e.U=n,e.C=!0,t=e;n&&n.C;)r=n.U,n===r.L?(o=r.R,o&&o.C?(n.C=o.C=!1,r.C=!0,t=r):(t===n.R&&(as(this,n),t=n,n=t.U),n.C=!1,r.C=!0,ss(this,r))):(o=r.L,o&&o.C?(n.C=o.C=!1,r.C=!0,t=r):(t===n.L&&(ss(this,n),t=n,n=t.U),n.C=!1,r.C=!0,as(this,r))),n=t.U;this._.C=!1},remove:function(t){t.N&&(t.N.P=t.P),t.P&&(t.P.N=t.N),t.N=t.P=null;var e,n,r,o=t.U,i=t.L,a=t.R;if(n=i?a?cs(a):i:a,o?o.L===t?o.L=n:o.R=n:this._=n,i&&a?(r=n.C,n.C=t.C,n.L=i,i.U=n,n!==a?(o=n.U,n.U=t.U,t=n.R,o.L=t,n.R=a,a.U=n):(n.U=o,o=n,t=n.R)):(r=t.C,t=n),t&&(t.U=o),!r){if(t&&t.C)return void(t.C=!1);do{if(t===this._)break;if(t===o.L){if(e=o.R,e.C&&(e.C=!1,o.C=!0,as(this,o),e=o.R),e.L&&e.L.C||e.R&&e.R.C){e.R&&e.R.C||(e.L.C=!1,e.C=!0,ss(this,e),e=o.R),e.C=o.C,o.C=e.R.C=!1,as(this,o),t=this._;break}}else if(e=o.L,e.C&&(e.C=!1,o.C=!0,ss(this,o),e=o.L),e.L&&e.L.C||e.R&&e.R.C){e.L&&e.L.C||(e.R.C=!1,e.C=!0,as(this,e),e=o.L),e.C=o.C,o.C=e.L.C=!1,ss(this,o),t=this._;break}e.C=!0,t=o,o=o.U}while(!t.C);t&&(t.C=!1)}}};var Wp,$p,Hp,Kp,Jp,Zp=[],Qp=[],tf=1e-6,ef=1e-12;Os.prototype={constructor:Os,polygons:function(){var t=this.edges;return this.cells.map(function(e){var n=e.halfedges.map(function(n){return ys(e,t[n])});return n.data=e.site.data,n})},triangles:function(){var t=[],e=this.edges;return this.cells.forEach(function(n,r){if(i=(o=n.halfedges).length)for(var o,i,a,s=n.site,c=-1,u=e[o[i-1]],l=u.left===s?u.right:u.left;++c<i;)a=l,u=e[o[c]],l=u.left===s?u.right:u.left,a&&l&&r<a.index&&r<l.index&&Ls(s,a,l)<0&&t.push([s.data,a.data,l.data])}),t},links:function(){return this.edges.filter(function(t){return t.right}).map(function(t){return{source:t.left.data,target:t.right.data}})},find:function(t,e,n){for(var r,o,i=this,a=i._found||0,s=i.cells.length;!(o=i.cells[a]);)if(++a>=s)return null;var c=t-o.site[0],u=e-o.site[1],l=c*c+u*u;do{o=i.cells[r=a],a=null,o.halfedges.forEach(function(n){var r=i.edges[n],s=r.left;if(s!==o.site&&s||(s=r.right)){var c=t-s[0],u=e-s[1],d=c*c+u*u;d<l&&(l=d,a=s.index)}})}while(null!==a);return i._found=r,null==n||l<=n*n?o.site:null}};var nf=function(){function t(t){return new Os(t.map(function(r,o){var i=[Math.round(e(r,o,t)/tf)*tf,Math.round(n(r,o,t)/tf)*tf];return i.index=o,i.data=r,i}),r)}var e=ns,n=rs,r=null;return t.polygons=function(e){return t(e).polygons()},t.links=function(e){return t(e).links()},t.triangles=function(e){return t(e).triangles()},t.x=function(n){return arguments.length?(e="function"==typeof n?n:Xp(+n),t):e},t.y=function(e){return arguments.length?(n="function"==typeof e?e:Xp(+e),t):n},t.extent=function(e){return arguments.length?(r=null==e?null:[[+e[0][0],+e[0][1]],[+e[1][0],+e[1][1]]],t):r&&[[r[0][0],r[0][1]],[r[1][0],r[1][1]]]},t.size=function(e){return arguments.length?(r=null==e?null:[[0,0],[+e[0],+e[1]]],t):r&&[r[1][0]-r[0][0],r[1][1]-r[0][1]]},t},rf=Object.freeze({randomPosition:zs,randomPoint:js,randomPolygon:Ys,randomLineString:Xs}),of=Object.freeze({getCluster:Zs,clusterEach:Qs,clusterReduce:tc,createBins:ec,applyFilter:nc,propertiesContainsFilter:rc,filterProperties:oc});/* Polyfill service v3.13.0
 * For detailed credits and licence information see http://github.com/financial-times/polyfill-service
 *
 * - Array.prototype.fill, License: CC0 */
"fill"in Array.prototype||Object.defineProperty(Array.prototype,"fill",{configurable:!0,value:function(t){if(void 0===this||null===this)throw new TypeError(this+" is not an object");var e=Object(this),n=Math.max(Math.min(e.length,9007199254740991),0)||0,r=1 in arguments?parseInt(Number(arguments[1]),10)||0:0;r=r<0?Math.max(n+r,0):Math.min(r,n);var o=2 in arguments&&void 0!==arguments[2]?parseInt(Number(arguments[2]),10)||0:n;for(o=o<0?Math.max(n+arguments[2],0):Math.min(o,n);r<o;)e[r]=t,++r;return e},writable:!0}),Number.isFinite=Number.isFinite||function(t){return"number"==typeof t&&isFinite(t)},Number.isInteger=Number.isInteger||function(t){return"number"==typeof t&&isFinite(t)&&Math.floor(t)===t},Number.parseFloat=Number.parseFloat||parseFloat,Number.isNaN=Number.isNaN||function(t){return t!==t},Math.trunc=Math.trunc||function(t){return t<0?Math.ceil(t):Math.floor(t)};var af=function(){};af.prototype.interfaces_=function(){return[]},af.prototype.getClass=function(){return af},af.prototype.equalsWithTolerance=function(t,e,n){return Math.abs(t-e)<=n};var sf=function(){},cf=function(){},uf={MAX_VALUE:{configurable:!0}};cf.isNaN=function(t){return Number.isNaN(t)},cf.doubleToLongBits=function(t){return t},cf.longBitsToDouble=function(t){return t},cf.isInfinite=function(t){return!Number.isFinite(t)},uf.MAX_VALUE.get=function(){return Number.MAX_VALUE},Object.defineProperties(cf,uf);var lf=function(){},df=function(){},pf=function(){},ff=function t(){if(this.x=null,this.y=null,this.z=null,0===arguments.length)this.x=0,this.y=0,this.z=t.NULL_ORDINATE;else if(1===arguments.length){var e=arguments[0];this.x=e.x,this.y=e.y,this.z=e.z}else 2===arguments.length?(this.x=arguments[0],this.y=arguments[1],this.z=t.NULL_ORDINATE):3===arguments.length&&(this.x=arguments[0],this.y=arguments[1],this.z=arguments[2])},hf={DimensionalComparator:{configurable:!0},serialVersionUID:{configurable:!0},NULL_ORDINATE:{configurable:!0},X:{configurable:!0},Y:{configurable:!0},Z:{configurable:!0}};ff.prototype.setOrdinate=function(t,e){switch(t){case ff.X:this.x=e;break;case ff.Y:this.y=e;break;case ff.Z:this.z=e;break;default:throw new sf("Invalid ordinate index: "+t)}},ff.prototype.equals2D=function(){if(1===arguments.length){var t=arguments[0];return this.x===t.x&&this.y===t.y}if(2===arguments.length){var e=arguments[0],n=arguments[1];return!!af.equalsWithTolerance(this.x,e.x,n)&&!!af.equalsWithTolerance(this.y,e.y,n)}},ff.prototype.getOrdinate=function(t){switch(t){case ff.X:return this.x;case ff.Y:return this.y;case ff.Z:return this.z}throw new sf("Invalid ordinate index: "+t)},ff.prototype.equals3D=function(t){return this.x===t.x&&this.y===t.y&&(this.z===t.z||cf.isNaN(this.z))&&cf.isNaN(t.z)},ff.prototype.equals=function(t){return t instanceof ff&&this.equals2D(t)},ff.prototype.equalInZ=function(t,e){return af.equalsWithTolerance(this.z,t.z,e)},ff.prototype.compareTo=function(t){var e=t;return this.x<e.x?-1:this.x>e.x?1:this.y<e.y?-1:this.y>e.y?1:0},ff.prototype.clone=function(){},ff.prototype.copy=function(){return new ff(this)},ff.prototype.toString=function(){return"("+this.x+", "+this.y+", "+this.z+")"},ff.prototype.distance3D=function(t){var e=this.x-t.x,n=this.y-t.y,r=this.z-t.z;return Math.sqrt(e*e+n*n+r*r)},ff.prototype.distance=function(t){var e=this.x-t.x,n=this.y-t.y;return Math.sqrt(e*e+n*n)},ff.prototype.hashCode=function(){var t=17;return t=37*t+ff.hashCode(this.x),t=37*t+ff.hashCode(this.y)},ff.prototype.setCoordinate=function(t){this.x=t.x,this.y=t.y,this.z=t.z},ff.prototype.interfaces_=function(){return[lf,df,ic]},ff.prototype.getClass=function(){return ff},ff.hashCode=function(){if(1===arguments.length){var t=arguments[0],e=cf.doubleToLongBits(t);return Math.trunc((e^e)>>>32)}},hf.DimensionalComparator.get=function(){return gf},hf.serialVersionUID.get=function(){return 0x5cbf2c235c7e5800},hf.NULL_ORDINATE.get=function(){return cf.NaN},hf.X.get=function(){return 0},hf.Y.get=function(){return 1},hf.Z.get=function(){return 2},Object.defineProperties(ff,hf);var gf=function(t){if(this._dimensionsToTest=2,0===arguments.length);else if(1===arguments.length){var e=arguments[0];if(2!==e&&3!==e)throw new sf("only 2 or 3 dimensions may be specified");this._dimensionsToTest=e}};gf.prototype.compare=function(t,e){var n=t,r=e,o=gf.compare(n.x,r.x);if(0!==o)return o;var i=gf.compare(n.y,r.y);return 0!==i?i:this._dimensionsToTest<=2?0:gf.compare(n.z,r.z)},gf.prototype.interfaces_=function(){return[pf]},gf.prototype.getClass=function(){return gf},gf.compare=function(t,e){return t<e?-1:t>e?1:cf.isNaN(t)?cf.isNaN(e)?0:-1:cf.isNaN(e)?1:0};var mf=function(){};mf.prototype.create=function(){},mf.prototype.interfaces_=function(){return[]},mf.prototype.getClass=function(){return mf};var yf=function(){},vf={INTERIOR:{configurable:!0},BOUNDARY:{configurable:!0},EXTERIOR:{configurable:!0},NONE:{configurable:!0}};yf.prototype.interfaces_=function(){return[]},yf.prototype.getClass=function(){return yf},yf.toLocationSymbol=function(t){switch(t){case yf.EXTERIOR:return"e";case yf.BOUNDARY:return"b";case yf.INTERIOR:return"i";case yf.NONE:return"-"}throw new sf("Unknown location value: "+t)},vf.INTERIOR.get=function(){return 0},vf.BOUNDARY.get=function(){return 1},vf.EXTERIOR.get=function(){return 2},vf.NONE.get=function(){return-1},Object.defineProperties(yf,vf);var _f=function(t,e){return t.interfaces_&&t.interfaces_().indexOf(e)>-1},bf=function(){},wf={LOG_10:{configurable:!0}};bf.prototype.interfaces_=function(){return[]},bf.prototype.getClass=function(){return bf},bf.log10=function(t){var e=Math.log(t);return cf.isInfinite(e)?e:cf.isNaN(e)?e:e/bf.LOG_10},bf.min=function(t,e,n,r){var o=t;return e<o&&(o=e),n<o&&(o=n),r<o&&(o=r),o},bf.clamp=function(){if("number"==typeof arguments[2]&&"number"==typeof arguments[0]&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1],n=arguments[2];return t<e?e:t>n?n:t}if(Number.isInteger(arguments[2])&&Number.isInteger(arguments[0])&&Number.isInteger(arguments[1])){var r=arguments[0],o=arguments[1],i=arguments[2];return r<o?o:r>i?i:r}},bf.wrap=function(t,e){return t<0?e- -t%e:t%e},bf.max=function(){if(3===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],r=t;return e>r&&(r=e),n>r&&(r=n),r}if(4===arguments.length){var o=arguments[0],i=arguments[1],a=arguments[2],s=arguments[3],c=o;return i>c&&(c=i),a>c&&(c=a),s>c&&(c=s),c}},bf.average=function(t,e){return(t+e)/2},wf.LOG_10.get=function(){return Math.log(10)},Object.defineProperties(bf,wf);var xf=function(t){this.str=t};xf.prototype.append=function(t){this.str+=t},xf.prototype.setCharAt=function(t,e){this.str=this.str.substr(0,t)+e+this.str.substr(t+1)},xf.prototype.toString=function(t){return this.str};var Ef=function(t){this.value=t};Ef.prototype.intValue=function(){return this.value},Ef.prototype.compareTo=function(t){return this.value<t?-1:this.value>t?1:0},Ef.isNaN=function(t){return Number.isNaN(t)};var Cf=function(){};Cf.isWhitespace=function(t){return t<=32&&t>=0||127===t},Cf.toUpperCase=function(t){return t.toUpperCase()};var Sf=function t(){if(this._hi=0,this._lo=0,0===arguments.length)this.init(0);else if(1===arguments.length){if("number"==typeof arguments[0]){var e=arguments[0];this.init(e)}else if(arguments[0]instanceof t){var n=arguments[0];this.init(n)}else if("string"==typeof arguments[0]){var r=arguments[0];t.call(this,t.parse(r))}}else if(2===arguments.length){var o=arguments[0],i=arguments[1];this.init(o,i)}},If={PI:{configurable:!0},TWO_PI:{configurable:!0},PI_2:{configurable:!0},E:{configurable:!0},NaN:{configurable:!0},EPS:{configurable:!0},SPLIT:{configurable:!0},MAX_PRINT_DIGITS:{configurable:!0},TEN:{configurable:!0},ONE:{configurable:!0},SCI_NOT_EXPONENT_CHAR:{configurable:!0},SCI_NOT_ZERO:{configurable:!0}};Sf.prototype.le=function(t){return(this._hi<t._hi||this._hi===t._hi)&&this._lo<=t._lo},Sf.prototype.extractSignificantDigits=function(t,e){var n=this.abs(),r=Sf.magnitude(n._hi),o=Sf.TEN.pow(r);n=n.divide(o),n.gt(Sf.TEN)?(n=n.divide(Sf.TEN),r+=1):n.lt(Sf.ONE)&&(n=n.multiply(Sf.TEN),r-=1);for(var i=r+1,a=new xf,s=Sf.MAX_PRINT_DIGITS-1,c=0;c<=s;c++){t&&c===i&&a.append(".");var u=Math.trunc(n._hi);if(u<0)break;var l=!1,d=0;u>9?(l=!0,d="9"):d="0"+u,a.append(d),n=n.subtract(Sf.valueOf(u)).multiply(Sf.TEN),l&&n.selfAdd(Sf.TEN);var p=!0,f=Sf.magnitude(n._hi);if(f<0&&Math.abs(f)>=s-c&&(p=!1),!p)break}return e[0]=r,a.toString()},Sf.prototype.sqr=function(){return this.multiply(this)},Sf.prototype.doubleValue=function(){return this._hi+this._lo},Sf.prototype.subtract=function(){if(arguments[0]instanceof Sf){var t=arguments[0];return this.add(t.negate())}if("number"==typeof arguments[0]){var e=arguments[0];return this.add(-e)}},Sf.prototype.equals=function(){if(1===arguments.length){var t=arguments[0];return this._hi===t._hi&&this._lo===t._lo}},Sf.prototype.isZero=function(){return 0===this._hi&&0===this._lo},Sf.prototype.selfSubtract=function(){if(arguments[0]instanceof Sf){var t=arguments[0];return this.isNaN()?this:this.selfAdd(-t._hi,-t._lo)}if("number"==typeof arguments[0]){var e=arguments[0];return this.isNaN()?this:this.selfAdd(-e,0)}},Sf.prototype.getSpecialNumberString=function(){return this.isZero()?"0.0":this.isNaN()?"NaN ":null},Sf.prototype.min=function(t){return this.le(t)?this:t},Sf.prototype.selfDivide=function(){if(1===arguments.length){if(arguments[0]instanceof Sf){var t=arguments[0];return this.selfDivide(t._hi,t._lo)}if("number"==typeof arguments[0]){var e=arguments[0];return this.selfDivide(e,0)}}else if(2===arguments.length){var n=arguments[0],r=arguments[1],o=null,i=null,a=null,s=null,c=null,u=null,l=null,d=null;return c=this._hi/n,u=Sf.SPLIT*c,o=u-c,d=Sf.SPLIT*n,o=u-o,i=c-o,a=d-n,l=c*n,a=d-a,s=n-a,d=o*a-l+o*s+i*a+i*s,u=(this._hi-l-d+this._lo-c*r)/n,d=c+u,this._hi=d,this._lo=c-d+u,this}},Sf.prototype.dump=function(){return"DD<"+this._hi+", "+this._lo+">"},Sf.prototype.divide=function(){if(arguments[0]instanceof Sf){var t=arguments[0],e=null,n=null,r=null,o=null,i=null,a=null,s=null,c=null;i=this._hi/t._hi,a=Sf.SPLIT*i,e=a-i,c=Sf.SPLIT*t._hi,e=a-e,n=i-e,r=c-t._hi,s=i*t._hi,r=c-r,o=t._hi-r,c=e*r-s+e*o+n*r+n*o,a=(this._hi-s-c+this._lo-i*t._lo)/t._hi,c=i+a;return new Sf(c,i-c+a)}if("number"==typeof arguments[0]){var u=arguments[0];return cf.isNaN(u)?Sf.createNaN():Sf.copy(this).selfDivide(u,0)}},Sf.prototype.ge=function(t){return(this._hi>t._hi||this._hi===t._hi)&&this._lo>=t._lo},Sf.prototype.pow=function(t){if(0===t)return Sf.valueOf(1);var e=new Sf(this),n=Sf.valueOf(1),r=Math.abs(t);if(r>1)for(;r>0;)r%2==1&&n.selfMultiply(e),(r/=2)>0&&(e=e.sqr());else n=e;return t<0?n.reciprocal():n},Sf.prototype.ceil=function(){if(this.isNaN())return Sf.NaN;var t=Math.ceil(this._hi),e=0;return t===this._hi&&(e=Math.ceil(this._lo)),new Sf(t,e)},Sf.prototype.compareTo=function(t){var e=t;return this._hi<e._hi?-1:this._hi>e._hi?1:this._lo<e._lo?-1:this._lo>e._lo?1:0},Sf.prototype.rint=function(){return this.isNaN()?this:this.add(.5).floor()},Sf.prototype.setValue=function(){if(arguments[0]instanceof Sf){var t=arguments[0];return this.init(t),this}if("number"==typeof arguments[0]){var e=arguments[0];return this.init(e),this}},Sf.prototype.max=function(t){return this.ge(t)?this:t},Sf.prototype.sqrt=function(){if(this.isZero())return Sf.valueOf(0);if(this.isNegative())return Sf.NaN;var t=1/Math.sqrt(this._hi),e=this._hi*t,n=Sf.valueOf(e),r=this.subtract(n.sqr()),o=r._hi*(.5*t);return n.add(o)},Sf.prototype.selfAdd=function(){if(1===arguments.length){if(arguments[0]instanceof Sf){var t=arguments[0];return this.selfAdd(t._hi,t._lo)}if("number"==typeof arguments[0]){var e=arguments[0],n=null,r=null,o=null,i=null,a=null,s=null;return o=this._hi+e,a=o-this._hi,i=o-a,i=e-a+(this._hi-i),s=i+this._lo,n=o+s,r=s+(o-n),this._hi=n+r,this._lo=r+(n-this._hi),this}}else if(2===arguments.length){var c=arguments[0],u=arguments[1],l=null,d=null,p=null,f=null,h=null,g=null,m=null,y=null;h=this._hi+c,p=this._lo+u,m=h-this._hi,y=p-this._lo,g=h-m,f=p-y,g=c-m+(this._hi-g),f=u-y+(this._lo-f),m=g+p,l=h+m,d=m+(h-l),m=f+d;var v=l+m,_=m+(l-v);return this._hi=v,this._lo=_,this}},Sf.prototype.selfMultiply=function(){if(1===arguments.length){if(arguments[0]instanceof Sf){var t=arguments[0];return this.selfMultiply(t._hi,t._lo)}if("number"==typeof arguments[0]){var e=arguments[0];return this.selfMultiply(e,0)}}else if(2===arguments.length){var n=arguments[0],r=arguments[1],o=null,i=null,a=null,s=null,c=null,u=null;c=Sf.SPLIT*this._hi,o=c-this._hi,u=Sf.SPLIT*n,o=c-o,i=this._hi-o,a=u-n,c=this._hi*n,a=u-a,s=n-a,u=o*a-c+o*s+i*a+i*s+(this._hi*r+this._lo*n);var l=c+u;o=c-l;var d=u+o;return this._hi=l,this._lo=d,this}},Sf.prototype.selfSqr=function(){return this.selfMultiply(this)},Sf.prototype.floor=function(){if(this.isNaN())return Sf.NaN;var t=Math.floor(this._hi),e=0;return t===this._hi&&(e=Math.floor(this._lo)),new Sf(t,e)},Sf.prototype.negate=function(){return this.isNaN()?this:new Sf(-this._hi,-this._lo)},Sf.prototype.clone=function(){},Sf.prototype.multiply=function(){if(arguments[0]instanceof Sf){var t=arguments[0];return t.isNaN()?Sf.createNaN():Sf.copy(this).selfMultiply(t)}if("number"==typeof arguments[0]){var e=arguments[0];return cf.isNaN(e)?Sf.createNaN():Sf.copy(this).selfMultiply(e,0)}},Sf.prototype.isNaN=function(){return cf.isNaN(this._hi)},Sf.prototype.intValue=function(){return Math.trunc(this._hi)},Sf.prototype.toString=function(){var t=Sf.magnitude(this._hi);return t>=-3&&t<=20?this.toStandardNotation():this.toSciNotation()},Sf.prototype.toStandardNotation=function(){var t=this.getSpecialNumberString();if(null!==t)return t;var e=new Array(1).fill(null),n=this.extractSignificantDigits(!0,e),r=e[0]+1,o=n;if("."===n.charAt(0))o="0"+n;else if(r<0)o="0."+Sf.stringOfChar("0",-r)+n;else if(-1===n.indexOf(".")){var i=r-n.length,a=Sf.stringOfChar("0",i);o=n+a+".0"}return this.isNegative()?"-"+o:o},Sf.prototype.reciprocal=function(){var t=null,e=null,n=null,r=null,o=null,i=null,a=null,s=null;o=1/this._hi,i=Sf.SPLIT*o,t=i-o,s=Sf.SPLIT*this._hi,t=i-t,e=o-t,n=s-this._hi,a=o*this._hi,n=s-n,r=this._hi-n,s=t*n-a+t*r+e*n+e*r,i=(1-a-s-o*this._lo)/this._hi;var c=o+i;return new Sf(c,o-c+i)},Sf.prototype.toSciNotation=function(){if(this.isZero())return Sf.SCI_NOT_ZERO;var t=this.getSpecialNumberString();if(null!==t)return t;var e=new Array(1).fill(null),n=this.extractSignificantDigits(!1,e),r=Sf.SCI_NOT_EXPONENT_CHAR+e[0];if("0"===n.charAt(0))throw new Error("Found leading zero: "+n);var o="";n.length>1&&(o=n.substring(1));var i=n.charAt(0)+"."+o;return this.isNegative()?"-"+i+r:i+r},Sf.prototype.abs=function(){return this.isNaN()?Sf.NaN:this.isNegative()?this.negate():new Sf(this)},Sf.prototype.isPositive=function(){return(this._hi>0||0===this._hi)&&this._lo>0},Sf.prototype.lt=function(t){return(this._hi<t._hi||this._hi===t._hi)&&this._lo<t._lo},Sf.prototype.add=function(){if(arguments[0]instanceof Sf){var t=arguments[0];return Sf.copy(this).selfAdd(t)}if("number"==typeof arguments[0]){var e=arguments[0];return Sf.copy(this).selfAdd(e)}},Sf.prototype.init=function(){if(1===arguments.length){if("number"==typeof arguments[0]){var t=arguments[0];this._hi=t,this._lo=0}else if(arguments[0]instanceof Sf){var e=arguments[0];this._hi=e._hi,this._lo=e._lo}}else if(2===arguments.length){var n=arguments[0],r=arguments[1];this._hi=n,this._lo=r}},Sf.prototype.gt=function(t){return(this._hi>t._hi||this._hi===t._hi)&&this._lo>t._lo},Sf.prototype.isNegative=function(){return(this._hi<0||0===this._hi)&&this._lo<0},Sf.prototype.trunc=function(){return this.isNaN()?Sf.NaN:this.isPositive()?this.floor():this.ceil()},Sf.prototype.signum=function(){return this._hi>0?1:this._hi<0?-1:this._lo>0?1:this._lo<0?-1:0},Sf.prototype.interfaces_=function(){return[ic,lf,df]},Sf.prototype.getClass=function(){return Sf},Sf.sqr=function(t){return Sf.valueOf(t).selfMultiply(t)},Sf.valueOf=function(){if("string"==typeof arguments[0]){var t=arguments[0];return Sf.parse(t)}if("number"==typeof arguments[0]){var e=arguments[0];return new Sf(e)}},Sf.sqrt=function(t){return Sf.valueOf(t).sqrt()},Sf.parse=function(t){for(var e=0,n=t.length;Cf.isWhitespace(t.charAt(e));)e++;var r=!1;if(e<n){var o=t.charAt(e);"-"!==o&&"+"!==o||(e++,"-"===o&&(r=!0))}for(var i=new Sf,a=0,s=0,c=0;;){if(e>=n)break;var u=t.charAt(e);if(e++,Cf.isDigit(u)){var l=u-"0";i.selfMultiply(Sf.TEN),i.selfAdd(l),a++}else{if("."!==u){if("e"===u||"E"===u){var d=t.substring(e);try{c=Ef.parseInt(d)}catch(e){throw e instanceof Error?new Error("Invalid exponent "+d+" in string "+t):e}break}throw new Error("Unexpected character '"+u+"' at position "+e+" in string "+t)}s=a}}var p=i,f=a-s-c;if(0===f)p=i;else if(f>0){var h=Sf.TEN.pow(f);p=i.divide(h)}else if(f<0){var g=Sf.TEN.pow(-f);p=i.multiply(g)}return r?p.negate():p},Sf.createNaN=function(){return new Sf(cf.NaN,cf.NaN)},Sf.copy=function(t){return new Sf(t)},Sf.magnitude=function(t){var e=Math.abs(t),n=Math.log(e)/Math.log(10),r=Math.trunc(Math.floor(n));return 10*Math.pow(10,r)<=e&&(r+=1),r},Sf.stringOfChar=function(t,e){for(var n=new xf,r=0;r<e;r++)n.append(t);return n.toString()},If.PI.get=function(){return new Sf(3.141592653589793,1.2246467991473532e-16)},If.TWO_PI.get=function(){return new Sf(6.283185307179586,2.4492935982947064e-16)},If.PI_2.get=function(){return new Sf(1.5707963267948966,6.123233995736766e-17)},If.E.get=function(){return new Sf(2.718281828459045,1.4456468917292502e-16)},If.NaN.get=function(){return new Sf(cf.NaN,cf.NaN)},If.EPS.get=function(){return 1.23259516440783e-32},If.SPLIT.get=function(){return 134217729},If.MAX_PRINT_DIGITS.get=function(){return 32},If.TEN.get=function(){return Sf.valueOf(10)},If.ONE.get=function(){return Sf.valueOf(1)},If.SCI_NOT_EXPONENT_CHAR.get=function(){return"E"},If.SCI_NOT_ZERO.get=function(){return"0.0E0"},Object.defineProperties(Sf,If);var Pf=function(){},Af={DP_SAFE_EPSILON:{configurable:!0}};Pf.prototype.interfaces_=function(){return[]},Pf.prototype.getClass=function(){return Pf},Pf.orientationIndex=function(t,e,n){var r=Pf.orientationIndexFilter(t,e,n);if(r<=1)return r;var o=Sf.valueOf(e.x).selfAdd(-t.x),i=Sf.valueOf(e.y).selfAdd(-t.y),a=Sf.valueOf(n.x).selfAdd(-e.x),s=Sf.valueOf(n.y).selfAdd(-e.y);return o.selfMultiply(s).selfSubtract(i.selfMultiply(a)).signum()},Pf.signOfDet2x2=function(t,e,n,r){return t.multiply(r).selfSubtract(e.multiply(n)).signum()},Pf.intersection=function(t,e,n,r){var o=Sf.valueOf(r.y).selfSubtract(n.y).selfMultiply(Sf.valueOf(e.x).selfSubtract(t.x)),i=Sf.valueOf(r.x).selfSubtract(n.x).selfMultiply(Sf.valueOf(e.y).selfSubtract(t.y)),a=o.subtract(i),s=Sf.valueOf(r.x).selfSubtract(n.x).selfMultiply(Sf.valueOf(t.y).selfSubtract(n.y)),c=Sf.valueOf(r.y).selfSubtract(n.y).selfMultiply(Sf.valueOf(t.x).selfSubtract(n.x)),u=s.subtract(c),l=u.selfDivide(a).doubleValue(),d=Sf.valueOf(t.x).selfAdd(Sf.valueOf(e.x).selfSubtract(t.x).selfMultiply(l)).doubleValue(),p=Sf.valueOf(e.x).selfSubtract(t.x).selfMultiply(Sf.valueOf(t.y).selfSubtract(n.y)),f=Sf.valueOf(e.y).selfSubtract(t.y).selfMultiply(Sf.valueOf(t.x).selfSubtract(n.x)),h=p.subtract(f),g=h.selfDivide(a).doubleValue(),m=Sf.valueOf(n.y).selfAdd(Sf.valueOf(r.y).selfSubtract(n.y).selfMultiply(g)).doubleValue();return new ff(d,m)},Pf.orientationIndexFilter=function(t,e,n){var r=null,o=(t.x-n.x)*(e.y-n.y),i=(t.y-n.y)*(e.x-n.x),a=o-i;if(o>0){if(i<=0)return Pf.signum(a);r=o+i}else{if(!(o<0))return Pf.signum(a);if(i>=0)return Pf.signum(a);r=-o-i}var s=Pf.DP_SAFE_EPSILON*r;return a>=s||-a>=s?Pf.signum(a):2},Pf.signum=function(t){return t>0?1:t<0?-1:0},Af.DP_SAFE_EPSILON.get=function(){return 1e-15},Object.defineProperties(Pf,Af);var Nf=function(){},Mf={X:{configurable:!0},Y:{configurable:!0},Z:{configurable:!0},M:{configurable:!0}};Mf.X.get=function(){return 0},Mf.Y.get=function(){return 1},Mf.Z.get=function(){return 2},Mf.M.get=function(){return 3},Nf.prototype.setOrdinate=function(t,e,n){},Nf.prototype.size=function(){},Nf.prototype.getOrdinate=function(t,e){},Nf.prototype.getCoordinate=function(){},Nf.prototype.getCoordinateCopy=function(t){},Nf.prototype.getDimension=function(){},Nf.prototype.getX=function(t){},Nf.prototype.clone=function(){},Nf.prototype.expandEnvelope=function(t){},Nf.prototype.copy=function(){},Nf.prototype.getY=function(t){},Nf.prototype.toCoordinateArray=function(){},Nf.prototype.interfaces_=function(){return[df]},Nf.prototype.getClass=function(){return Nf},Object.defineProperties(Nf,Mf);var Lf=function(){},Df=function(t){function e(){t.call(this,"Projective point not representable on the Cartesian plane.")}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(Lf),Of=function(){};Of.arraycopy=function(t,e,n,r,o){for(var i=0,a=e;a<e+o;a++)n[r+i]=t[a],i++},Of.getProperty=function(t){return{"line.separator":"\n"}[t]};var Tf=function t(){if(this.x=null,this.y=null,this.w=null,0===arguments.length)this.x=0,this.y=0,this.w=1;else if(1===arguments.length){var e=arguments[0];this.x=e.x,this.y=e.y,this.w=1}else if(2===arguments.length){if("number"==typeof arguments[0]&&"number"==typeof arguments[1]){var n=arguments[0],r=arguments[1];this.x=n,this.y=r,this.w=1}else if(arguments[0]instanceof t&&arguments[1]instanceof t){var o=arguments[0],i=arguments[1];this.x=o.y*i.w-i.y*o.w,this.y=i.x*o.w-o.x*i.w,this.w=o.x*i.y-i.x*o.y}else if(arguments[0]instanceof ff&&arguments[1]instanceof ff){var a=arguments[0],s=arguments[1];this.x=a.y-s.y,this.y=s.x-a.x,this.w=a.x*s.y-s.x*a.y}}else if(3===arguments.length){var c=arguments[0],u=arguments[1],l=arguments[2];this.x=c,this.y=u,this.w=l}else if(4===arguments.length){var d=arguments[0],p=arguments[1],f=arguments[2],h=arguments[3],g=d.y-p.y,m=p.x-d.x,y=d.x*p.y-p.x*d.y,v=f.y-h.y,_=h.x-f.x,b=f.x*h.y-h.x*f.y;this.x=m*b-_*y,this.y=v*y-g*b,this.w=g*_-v*m}};Tf.prototype.getY=function(){var t=this.y/this.w;if(cf.isNaN(t)||cf.isInfinite(t))throw new Df;return t},Tf.prototype.getX=function(){var t=this.x/this.w;if(cf.isNaN(t)||cf.isInfinite(t))throw new Df;return t},Tf.prototype.getCoordinate=function(){var t=new ff;return t.x=this.getX(),t.y=this.getY(),t},Tf.prototype.interfaces_=function(){return[]},Tf.prototype.getClass=function(){return Tf},Tf.intersection=function(t,e,n,r){var o=t.y-e.y,i=e.x-t.x,a=t.x*e.y-e.x*t.y,s=n.y-r.y,c=r.x-n.x,u=n.x*r.y-r.x*n.y,l=i*u-c*a,d=s*a-o*u,p=o*c-s*i,f=l/p,h=d/p;if(cf.isNaN(f)||cf.isInfinite(f)||cf.isNaN(h)||cf.isInfinite(h))throw new Df;return new ff(f,h)};var Rf=function t(){if(this._minx=null,this._maxx=null,this._miny=null,this._maxy=null,0===arguments.length)this.init();else if(1===arguments.length){if(arguments[0]instanceof ff){var e=arguments[0];this.init(e.x,e.x,e.y,e.y)}else if(arguments[0]instanceof t){var n=arguments[0];this.init(n)}}else if(2===arguments.length){var r=arguments[0],o=arguments[1];this.init(r.x,o.x,r.y,o.y)}else if(4===arguments.length){var i=arguments[0],a=arguments[1],s=arguments[2],c=arguments[3];this.init(i,a,s,c)}},Ff={serialVersionUID:{configurable:!0}};Rf.prototype.getArea=function(){return this.getWidth()*this.getHeight()},Rf.prototype.equals=function(t){if(!(t instanceof Rf))return!1;var e=t;return this.isNull()?e.isNull():this._maxx===e.getMaxX()&&this._maxy===e.getMaxY()&&this._minx===e.getMinX()&&this._miny===e.getMinY()},Rf.prototype.intersection=function(t){if(this.isNull()||t.isNull()||!this.intersects(t))return new Rf;var e=this._minx>t._minx?this._minx:t._minx,n=this._miny>t._miny?this._miny:t._miny,r=this._maxx<t._maxx?this._maxx:t._maxx,o=this._maxy<t._maxy?this._maxy:t._maxy;return new Rf(e,r,n,o)},Rf.prototype.isNull=function(){return this._maxx<this._minx},Rf.prototype.getMaxX=function(){return this._maxx},Rf.prototype.covers=function(){if(1===arguments.length){if(arguments[0]instanceof ff){var t=arguments[0];return this.covers(t.x,t.y)}if(arguments[0]instanceof Rf){var e=arguments[0];return!this.isNull()&&!e.isNull()&&(e.getMinX()>=this._minx&&e.getMaxX()<=this._maxx&&e.getMinY()>=this._miny&&e.getMaxY()<=this._maxy)}}else if(2===arguments.length){var n=arguments[0],r=arguments[1];return!this.isNull()&&(n>=this._minx&&n<=this._maxx&&r>=this._miny&&r<=this._maxy)}},Rf.prototype.intersects=function(){if(1===arguments.length){if(arguments[0]instanceof Rf){var t=arguments[0];return!this.isNull()&&!t.isNull()&&!(t._minx>this._maxx||t._maxx<this._minx||t._miny>this._maxy||t._maxy<this._miny)}if(arguments[0]instanceof ff){var e=arguments[0];return this.intersects(e.x,e.y)}}else if(2===arguments.length){var n=arguments[0],r=arguments[1];return!this.isNull()&&!(n>this._maxx||n<this._minx||r>this._maxy||r<this._miny)}},Rf.prototype.getMinY=function(){return this._miny},Rf.prototype.getMinX=function(){return this._minx},Rf.prototype.expandToInclude=function(){if(1===arguments.length){if(arguments[0]instanceof ff){var t=arguments[0];this.expandToInclude(t.x,t.y)}else if(arguments[0]instanceof Rf){var e=arguments[0];if(e.isNull())return null;this.isNull()?(this._minx=e.getMinX(),this._maxx=e.getMaxX(),this._miny=e.getMinY(),this._maxy=e.getMaxY()):(e._minx<this._minx&&(this._minx=e._minx),e._maxx>this._maxx&&(this._maxx=e._maxx),e._miny<this._miny&&(this._miny=e._miny),e._maxy>this._maxy&&(this._maxy=e._maxy))}}else if(2===arguments.length){var n=arguments[0],r=arguments[1];this.isNull()?(this._minx=n,this._maxx=n,this._miny=r,this._maxy=r):(n<this._minx&&(this._minx=n),n>this._maxx&&(this._maxx=n),r<this._miny&&(this._miny=r),r>this._maxy&&(this._maxy=r))}},Rf.prototype.minExtent=function(){if(this.isNull())return 0;var t=this.getWidth(),e=this.getHeight();return t<e?t:e},Rf.prototype.getWidth=function(){return this.isNull()?0:this._maxx-this._minx},Rf.prototype.compareTo=function(t){var e=t;return this.isNull()?e.isNull()?0:-1:e.isNull()?1:this._minx<e._minx?-1:this._minx>e._minx?1:this._miny<e._miny?-1:this._miny>e._miny?1:this._maxx<e._maxx?-1:this._maxx>e._maxx?1:this._maxy<e._maxy?-1:this._maxy>e._maxy?1:0},Rf.prototype.translate=function(t,e){if(this.isNull())return null;this.init(this.getMinX()+t,this.getMaxX()+t,this.getMinY()+e,this.getMaxY()+e)},Rf.prototype.toString=function(){return"Env["+this._minx+" : "+this._maxx+", "+this._miny+" : "+this._maxy+"]"},Rf.prototype.setToNull=function(){this._minx=0,this._maxx=-1,this._miny=0,this._maxy=-1},Rf.prototype.getHeight=function(){return this.isNull()?0:this._maxy-this._miny},Rf.prototype.maxExtent=function(){if(this.isNull())return 0;var t=this.getWidth(),e=this.getHeight();return t>e?t:e},Rf.prototype.expandBy=function(){if(1===arguments.length){var t=arguments[0];this.expandBy(t,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(this.isNull())return null;this._minx-=e,this._maxx+=e,this._miny-=n,this._maxy+=n,(this._minx>this._maxx||this._miny>this._maxy)&&this.setToNull()}},Rf.prototype.contains=function(){if(1===arguments.length){if(arguments[0]instanceof Rf){var t=arguments[0];return this.covers(t)}if(arguments[0]instanceof ff){var e=arguments[0];return this.covers(e)}}else if(2===arguments.length){var n=arguments[0],r=arguments[1];return this.covers(n,r)}},Rf.prototype.centre=function(){return this.isNull()?null:new ff((this.getMinX()+this.getMaxX())/2,(this.getMinY()+this.getMaxY())/2)},Rf.prototype.init=function(){if(0===arguments.length)this.setToNull();else if(1===arguments.length){if(arguments[0]instanceof ff){var t=arguments[0];this.init(t.x,t.x,t.y,t.y)}else if(arguments[0]instanceof Rf){var e=arguments[0];this._minx=e._minx,this._maxx=e._maxx,this._miny=e._miny,this._maxy=e._maxy}}else if(2===arguments.length){var n=arguments[0],r=arguments[1];this.init(n.x,r.x,n.y,r.y)}else if(4===arguments.length){var o=arguments[0],i=arguments[1],a=arguments[2],s=arguments[3];o<i?(this._minx=o,this._maxx=i):(this._minx=i,this._maxx=o),a<s?(this._miny=a,this._maxy=s):(this._miny=s,this._maxy=a)}},Rf.prototype.getMaxY=function(){return this._maxy},Rf.prototype.distance=function(t){if(this.intersects(t))return 0;var e=0;this._maxx<t._minx?e=t._minx-this._maxx:this._minx>t._maxx&&(e=this._minx-t._maxx);var n=0;return this._maxy<t._miny?n=t._miny-this._maxy:this._miny>t._maxy&&(n=this._miny-t._maxy),0===e?n:0===n?e:Math.sqrt(e*e+n*n)},Rf.prototype.hashCode=function(){var t=17;return t=37*t+ff.hashCode(this._minx),t=37*t+ff.hashCode(this._maxx),t=37*t+ff.hashCode(this._miny),t=37*t+ff.hashCode(this._maxy)},Rf.prototype.interfaces_=function(){return[lf,ic]},Rf.prototype.getClass=function(){return Rf},Rf.intersects=function(){if(3===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2];return n.x>=(t.x<e.x?t.x:e.x)&&n.x<=(t.x>e.x?t.x:e.x)&&n.y>=(t.y<e.y?t.y:e.y)&&n.y<=(t.y>e.y?t.y:e.y)}if(4===arguments.length){var r=arguments[0],o=arguments[1],i=arguments[2],a=arguments[3],s=Math.min(i.x,a.x),c=Math.max(i.x,a.x),u=Math.min(r.x,o.x),l=Math.max(r.x,o.x);return!(u>c)&&(!(l<s)&&(s=Math.min(i.y,a.y),c=Math.max(i.y,a.y),u=Math.min(r.y,o.y),l=Math.max(r.y,o.y),!(u>c)&&!(l<s)))}},Ff.serialVersionUID.get=function(){return 0x51845cd552189800},Object.defineProperties(Rf,Ff);var Gf={typeStr:/^\s*(\w+)\s*\(\s*(.*)\s*\)\s*$/,emptyTypeStr:/^\s*(\w+)\s*EMPTY\s*$/,spaces:/\s+/,parenComma:/\)\s*,\s*\(/,doubleParenComma:/\)\s*\)\s*,\s*\(\s*\(/,trimParens:/^\s*\(?(.*?)\)?\s*$/},qf=function(t){this.geometryFactory=t||new cg};qf.prototype.read=function(t){var e,n,r;t=t.replace(/[\n\r]/g," ");var o=Gf.typeStr.exec(t);if(-1!==t.search("EMPTY")&&(o=Gf.emptyTypeStr.exec(t),o[2]=void 0),o&&(n=o[1].toLowerCase(),r=o[2],Uf[n]&&(e=Uf[n].apply(this,[r]))),void 0===e)throw new Error("Could not parse WKT "+t);return e},qf.prototype.write=function(t){return this.extractGeometry(t)},qf.prototype.extractGeometry=function(t){var e=t.getGeometryType().toLowerCase();if(!kf[e])return null;var n=e.toUpperCase();return t.isEmpty()?n+" EMPTY":n+"("+kf[e].apply(this,[t])+")"};var kf={coordinate:function(t){return t.x+" "+t.y},point:function(t){return kf.coordinate.call(this,t._coordinates._coordinates[0])},multipoint:function(t){for(var e=this,n=[],r=0,o=t._geometries.length;r<o;++r)n.push("("+kf.point.apply(e,[t._geometries[r]])+")");return n.join(",")},linestring:function(t){for(var e=this,n=[],r=0,o=t._points._coordinates.length;r<o;++r)n.push(kf.coordinate.apply(e,[t._points._coordinates[r]]));return n.join(",")},linearring:function(t){for(var e=this,n=[],r=0,o=t._points._coordinates.length;r<o;++r)n.push(kf.coordinate.apply(e,[t._points._coordinates[r]]));return n.join(",")},multilinestring:function(t){for(var e=this,n=[],r=0,o=t._geometries.length;r<o;++r)n.push("("+kf.linestring.apply(e,[t._geometries[r]])+")");return n.join(",")},polygon:function(t){var e=this,n=[];n.push("("+kf.linestring.apply(this,[t._shell])+")");for(var r=0,o=t._holes.length;r<o;++r)n.push("("+kf.linestring.apply(e,[t._holes[r]])+")");return n.join(",")},multipolygon:function(t){for(var e=this,n=[],r=0,o=t._geometries.length;r<o;++r)n.push("("+kf.polygon.apply(e,[t._geometries[r]])+")");return n.join(",")},geometrycollection:function(t){for(var e=this,n=[],r=0,o=t._geometries.length;r<o;++r)n.push(e.extractGeometry(t._geometries[r]));return n.join(",")}},Uf={point:function(t){if(void 0===t)return this.geometryFactory.createPoint();var e=t.trim().split(Gf.spaces);return this.geometryFactory.createPoint(new ff(Number.parseFloat(e[0]),Number.parseFloat(e[1])))},multipoint:function(t){var e=this;if(void 0===t)return this.geometryFactory.createMultiPoint();for(var n,r=t.trim().split(","),o=[],i=0,a=r.length;i<a;++i)n=r[i].replace(Gf.trimParens,"$1"),o.push(Uf.point.apply(e,[n]));return this.geometryFactory.createMultiPoint(o)},linestring:function(t){if(void 0===t)return this.geometryFactory.createLineString();for(var e,n=t.trim().split(","),r=[],o=0,i=n.length;o<i;++o)e=n[o].trim().split(Gf.spaces),r.push(new ff(Number.parseFloat(e[0]),Number.parseFloat(e[1])));return this.geometryFactory.createLineString(r)},linearring:function(t){if(void 0===t)return this.geometryFactory.createLinearRing();for(var e,n=t.trim().split(","),r=[],o=0,i=n.length;o<i;++o)e=n[o].trim().split(Gf.spaces),
r.push(new ff(Number.parseFloat(e[0]),Number.parseFloat(e[1])));return this.geometryFactory.createLinearRing(r)},multilinestring:function(t){var e=this;if(void 0===t)return this.geometryFactory.createMultiLineString();for(var n,r=t.trim().split(Gf.parenComma),o=[],i=0,a=r.length;i<a;++i)n=r[i].replace(Gf.trimParens,"$1"),o.push(Uf.linestring.apply(e,[n]));return this.geometryFactory.createMultiLineString(o)},polygon:function(t){var e=this;if(void 0===t)return this.geometryFactory.createPolygon();for(var n,r,o,i,a=t.trim().split(Gf.parenComma),s=[],c=0,u=a.length;c<u;++c)n=a[c].replace(Gf.trimParens,"$1"),r=Uf.linestring.apply(e,[n]),o=e.geometryFactory.createLinearRing(r._points),0===c?i=o:s.push(o);return this.geometryFactory.createPolygon(i,s)},multipolygon:function(t){var e=this;if(void 0===t)return this.geometryFactory.createMultiPolygon();for(var n,r=t.trim().split(Gf.doubleParenComma),o=[],i=0,a=r.length;i<a;++i)n=r[i].replace(Gf.trimParens,"$1"),o.push(Uf.polygon.apply(e,[n]));return this.geometryFactory.createMultiPolygon(o)},geometrycollection:function(t){var e=this;if(void 0===t)return this.geometryFactory.createGeometryCollection();t=t.replace(/,\s*([A-Za-z])/g,"|$1");for(var n=t.trim().split("|"),r=[],o=0,i=n.length;o<i;++o)r.push(e.read(n[o]));return this.geometryFactory.createGeometryCollection(r)}},Bf=function(t){this.parser=new qf(t)};Bf.prototype.write=function(t){return this.parser.write(t)},Bf.toLineString=function(t,e){if(2!==arguments.length)throw new Error("Not implemented");return"LINESTRING ( "+t.x+" "+t.y+", "+e.x+" "+e.y+" )"};var Vf=function(t){function e(e){t.call(this,e),this.name="RuntimeException",this.message=e,this.stack=(new t).stack}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e}(Error),zf=function(t){function e(){if(t.call(this),0===arguments.length)t.call(this);else if(1===arguments.length){var e=arguments[0];t.call(this,e)}}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(Vf),jf=function(){};jf.prototype.interfaces_=function(){return[]},jf.prototype.getClass=function(){return jf},jf.shouldNeverReachHere=function(){if(0===arguments.length)jf.shouldNeverReachHere(null);else if(1===arguments.length){var t=arguments[0];throw new zf("Should never reach here"+(null!==t?": "+t:""))}},jf.isTrue=function(){var t,e;if(1===arguments.length)t=arguments[0],jf.isTrue(t,null);else if(2===arguments.length&&(t=arguments[0],e=arguments[1],!t))throw null===e?new zf:new zf(e)},jf.equals=function(){var t,e,n;if(2===arguments.length)t=arguments[0],e=arguments[1],jf.equals(t,e,null);else if(3===arguments.length&&(t=arguments[0],e=arguments[1],n=arguments[2],!e.equals(t)))throw new zf("Expected "+t+" but encountered "+e+(null!==n?": "+n:""))};var Yf=function(){this._result=null,this._inputLines=Array(2).fill().map(function(){return Array(2)}),this._intPt=new Array(2).fill(null),this._intLineIndex=null,this._isProper=null,this._pa=null,this._pb=null,this._precisionModel=null,this._intPt[0]=new ff,this._intPt[1]=new ff,this._pa=this._intPt[0],this._pb=this._intPt[1],this._result=0},Xf={DONT_INTERSECT:{configurable:!0},DO_INTERSECT:{configurable:!0},COLLINEAR:{configurable:!0},NO_INTERSECTION:{configurable:!0},POINT_INTERSECTION:{configurable:!0},COLLINEAR_INTERSECTION:{configurable:!0}};Yf.prototype.getIndexAlongSegment=function(t,e){return this.computeIntLineIndex(),this._intLineIndex[t][e]},Yf.prototype.getTopologySummary=function(){var t=new xf;return this.isEndPoint()&&t.append(" endpoint"),this._isProper&&t.append(" proper"),this.isCollinear()&&t.append(" collinear"),t.toString()},Yf.prototype.computeIntersection=function(t,e,n,r){this._inputLines[0][0]=t,this._inputLines[0][1]=e,this._inputLines[1][0]=n,this._inputLines[1][1]=r,this._result=this.computeIntersect(t,e,n,r)},Yf.prototype.getIntersectionNum=function(){return this._result},Yf.prototype.computeIntLineIndex=function(){if(0===arguments.length)null===this._intLineIndex&&(this._intLineIndex=Array(2).fill().map(function(){return Array(2)}),this.computeIntLineIndex(0),this.computeIntLineIndex(1));else if(1===arguments.length){var t=arguments[0],e=this.getEdgeDistance(t,0),n=this.getEdgeDistance(t,1);e>n?(this._intLineIndex[t][0]=0,this._intLineIndex[t][1]=1):(this._intLineIndex[t][0]=1,this._intLineIndex[t][1]=0)}},Yf.prototype.isProper=function(){return this.hasIntersection()&&this._isProper},Yf.prototype.setPrecisionModel=function(t){this._precisionModel=t},Yf.prototype.isInteriorIntersection=function(){var t=this;if(0===arguments.length)return!!this.isInteriorIntersection(0)||!!this.isInteriorIntersection(1);if(1===arguments.length){for(var e=arguments[0],n=0;n<this._result;n++)if(!t._intPt[n].equals2D(t._inputLines[e][0])&&!t._intPt[n].equals2D(t._inputLines[e][1]))return!0;return!1}},Yf.prototype.getIntersection=function(t){return this._intPt[t]},Yf.prototype.isEndPoint=function(){return this.hasIntersection()&&!this._isProper},Yf.prototype.hasIntersection=function(){return this._result!==Yf.NO_INTERSECTION},Yf.prototype.getEdgeDistance=function(t,e){return Yf.computeEdgeDistance(this._intPt[e],this._inputLines[t][0],this._inputLines[t][1])},Yf.prototype.isCollinear=function(){return this._result===Yf.COLLINEAR_INTERSECTION},Yf.prototype.toString=function(){return Bf.toLineString(this._inputLines[0][0],this._inputLines[0][1])+" - "+Bf.toLineString(this._inputLines[1][0],this._inputLines[1][1])+this.getTopologySummary()},Yf.prototype.getEndpoint=function(t,e){return this._inputLines[t][e]},Yf.prototype.isIntersection=function(t){for(var e=this,n=0;n<this._result;n++)if(e._intPt[n].equals2D(t))return!0;return!1},Yf.prototype.getIntersectionAlongSegment=function(t,e){return this.computeIntLineIndex(),this._intPt[this._intLineIndex[t][e]]},Yf.prototype.interfaces_=function(){return[]},Yf.prototype.getClass=function(){return Yf},Yf.computeEdgeDistance=function(t,e,n){var r=Math.abs(n.x-e.x),o=Math.abs(n.y-e.y),i=-1;if(t.equals(e))i=0;else if(t.equals(n))i=r>o?r:o;else{var a=Math.abs(t.x-e.x),s=Math.abs(t.y-e.y);i=r>o?a:s,0!==i||t.equals(e)||(i=Math.max(a,s))}return jf.isTrue(!(0===i&&!t.equals(e)),"Bad distance calculation"),i},Yf.nonRobustComputeEdgeDistance=function(t,e,n){var r=t.x-e.x,o=t.y-e.y,i=Math.sqrt(r*r+o*o);return jf.isTrue(!(0===i&&!t.equals(e)),"Invalid distance calculation"),i},Xf.DONT_INTERSECT.get=function(){return 0},Xf.DO_INTERSECT.get=function(){return 1},Xf.COLLINEAR.get=function(){return 2},Xf.NO_INTERSECTION.get=function(){return 0},Xf.POINT_INTERSECTION.get=function(){return 1},Xf.COLLINEAR_INTERSECTION.get=function(){return 2},Object.defineProperties(Yf,Xf);var Wf=function(t){function e(){t.apply(this,arguments)}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.isInSegmentEnvelopes=function(t){var e=new Rf(this._inputLines[0][0],this._inputLines[0][1]),n=new Rf(this._inputLines[1][0],this._inputLines[1][1]);return e.contains(t)&&n.contains(t)},e.prototype.computeIntersection=function(){if(3!==arguments.length)return t.prototype.computeIntersection.apply(this,arguments);var e=arguments[0],n=arguments[1],r=arguments[2];if(this._isProper=!1,Rf.intersects(n,r,e)&&0===Kf.orientationIndex(n,r,e)&&0===Kf.orientationIndex(r,n,e))return this._isProper=!0,(e.equals(n)||e.equals(r))&&(this._isProper=!1),this._result=t.POINT_INTERSECTION,null;this._result=t.NO_INTERSECTION},e.prototype.normalizeToMinimum=function(t,e,n,r,o){o.x=this.smallestInAbsValue(t.x,e.x,n.x,r.x),o.y=this.smallestInAbsValue(t.y,e.y,n.y,r.y),t.x-=o.x,t.y-=o.y,e.x-=o.x,e.y-=o.y,n.x-=o.x,n.y-=o.y,r.x-=o.x,r.y-=o.y},e.prototype.safeHCoordinateIntersection=function(t,n,r,o){var i=null;try{i=Tf.intersection(t,n,r,o)}catch(a){if(!(a instanceof Df))throw a;i=e.nearestEndpoint(t,n,r,o)}return i},e.prototype.intersection=function(t,n,r,o){var i=this.intersectionWithNormalization(t,n,r,o);return this.isInSegmentEnvelopes(i)||(i=new ff(e.nearestEndpoint(t,n,r,o))),null!==this._precisionModel&&this._precisionModel.makePrecise(i),i},e.prototype.smallestInAbsValue=function(t,e,n,r){var o=t,i=Math.abs(o);return Math.abs(e)<i&&(o=e,i=Math.abs(e)),Math.abs(n)<i&&(o=n,i=Math.abs(n)),Math.abs(r)<i&&(o=r),o},e.prototype.checkDD=function(t,e,n,r,o){var i=Pf.intersection(t,e,n,r),a=this.isInSegmentEnvelopes(i);Of.out.println("DD in env = "+a+"  --------------------- "+i),o.distance(i)>1e-4&&Of.out.println("Distance = "+o.distance(i))},e.prototype.intersectionWithNormalization=function(t,e,n,r){var o=new ff(t),i=new ff(e),a=new ff(n),s=new ff(r),c=new ff;this.normalizeToEnvCentre(o,i,a,s,c);var u=this.safeHCoordinateIntersection(o,i,a,s);return u.x+=c.x,u.y+=c.y,u},e.prototype.computeCollinearIntersection=function(e,n,r,o){var i=Rf.intersects(e,n,r),a=Rf.intersects(e,n,o),s=Rf.intersects(r,o,e),c=Rf.intersects(r,o,n);return i&&a?(this._intPt[0]=r,this._intPt[1]=o,t.COLLINEAR_INTERSECTION):s&&c?(this._intPt[0]=e,this._intPt[1]=n,t.COLLINEAR_INTERSECTION):i&&s?(this._intPt[0]=r,this._intPt[1]=e,!r.equals(e)||a||c?t.COLLINEAR_INTERSECTION:t.POINT_INTERSECTION):i&&c?(this._intPt[0]=r,this._intPt[1]=n,!r.equals(n)||a||s?t.COLLINEAR_INTERSECTION:t.POINT_INTERSECTION):a&&s?(this._intPt[0]=o,this._intPt[1]=e,!o.equals(e)||i||c?t.COLLINEAR_INTERSECTION:t.POINT_INTERSECTION):a&&c?(this._intPt[0]=o,this._intPt[1]=n,!o.equals(n)||i||s?t.COLLINEAR_INTERSECTION:t.POINT_INTERSECTION):t.NO_INTERSECTION},e.prototype.normalizeToEnvCentre=function(t,e,n,r,o){var i=t.x<e.x?t.x:e.x,a=t.y<e.y?t.y:e.y,s=t.x>e.x?t.x:e.x,c=t.y>e.y?t.y:e.y,u=n.x<r.x?n.x:r.x,l=n.y<r.y?n.y:r.y,d=n.x>r.x?n.x:r.x,p=n.y>r.y?n.y:r.y,f=i>u?i:u,h=s<d?s:d,g=a>l?a:l,m=c<p?c:p,y=(f+h)/2,v=(g+m)/2;o.x=y,o.y=v,t.x-=o.x,t.y-=o.y,e.x-=o.x,e.y-=o.y,n.x-=o.x,n.y-=o.y,r.x-=o.x,r.y-=o.y},e.prototype.computeIntersect=function(e,n,r,o){if(this._isProper=!1,!Rf.intersects(e,n,r,o))return t.NO_INTERSECTION;var i=Kf.orientationIndex(e,n,r),a=Kf.orientationIndex(e,n,o);if(i>0&&a>0||i<0&&a<0)return t.NO_INTERSECTION;var s=Kf.orientationIndex(r,o,e),c=Kf.orientationIndex(r,o,n);return s>0&&c>0||s<0&&c<0?t.NO_INTERSECTION:0===i&&0===a&&0===s&&0===c?this.computeCollinearIntersection(e,n,r,o):(0===i||0===a||0===s||0===c?(this._isProper=!1,e.equals2D(r)||e.equals2D(o)?this._intPt[0]=e:n.equals2D(r)||n.equals2D(o)?this._intPt[0]=n:0===i?this._intPt[0]=new ff(r):0===a?this._intPt[0]=new ff(o):0===s?this._intPt[0]=new ff(e):0===c&&(this._intPt[0]=new ff(n))):(this._isProper=!0,this._intPt[0]=this.intersection(e,n,r,o)),t.POINT_INTERSECTION)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e.nearestEndpoint=function(t,e,n,r){var o=t,i=Kf.distancePointLine(t,n,r),a=Kf.distancePointLine(e,n,r);return a<i&&(i=a,o=e),a=Kf.distancePointLine(n,t,e),a<i&&(i=a,o=n),a=Kf.distancePointLine(r,t,e),a<i&&(i=a,o=r),o},e}(Yf),$f=function(){};$f.prototype.interfaces_=function(){return[]},$f.prototype.getClass=function(){return $f},$f.orientationIndex=function(t,e,n){var r=e.x-t.x,o=e.y-t.y,i=n.x-e.x,a=n.y-e.y;return $f.signOfDet2x2(r,o,i,a)},$f.signOfDet2x2=function(t,e,n,r){var o=null,i=null,a=null;if(o=1,0===t||0===r)return 0===e||0===n?0:e>0?n>0?-o:o:n>0?o:-o;if(0===e||0===n)return r>0?t>0?o:-o:t>0?-o:o;if(e>0?r>0?e<=r||(o=-o,i=t,t=n,n=i,i=e,e=r,r=i):e<=-r?(o=-o,n=-n,r=-r):(i=t,t=-n,n=i,i=e,e=-r,r=i):r>0?-e<=r?(o=-o,t=-t,e=-e):(i=-t,t=n,n=i,i=-e,e=r,r=i):e>=r?(t=-t,e=-e,n=-n,r=-r):(o=-o,i=-t,t=-n,n=i,i=-e,e=-r,r=i),t>0){if(!(n>0))return o;if(!(t<=n))return o}else{if(n>0)return-o;if(!(t>=n))return-o;o=-o,t=-t,n=-n}for(;;){if(a=Math.floor(n/t),n-=a*t,(r-=a*e)<0)return-o;if(r>e)return o;if(t>n+n){if(e<r+r)return o}else{if(e>r+r)return-o;n=t-n,r=e-r,o=-o}if(0===r)return 0===n?0:-o;if(0===n)return o;if(a=Math.floor(t/n),t-=a*n,(e-=a*r)<0)return o;if(e>r)return-o;if(n>t+t){if(r<e+e)return-o}else{if(r>e+e)return o;t=n-t,e=r-e,o=-o}if(0===e)return 0===t?0:o;if(0===t)return-o}};var Hf=function(){this._p=null,this._crossingCount=0,this._isPointOnSegment=!1;var t=arguments[0];this._p=t};Hf.prototype.countSegment=function(t,e){if(t.x<this._p.x&&e.x<this._p.x)return null;if(this._p.x===e.x&&this._p.y===e.y)return this._isPointOnSegment=!0,null;if(t.y===this._p.y&&e.y===this._p.y){var n=t.x,r=e.x;return n>r&&(n=e.x,r=t.x),this._p.x>=n&&this._p.x<=r&&(this._isPointOnSegment=!0),null}if(t.y>this._p.y&&e.y<=this._p.y||e.y>this._p.y&&t.y<=this._p.y){var o=t.x-this._p.x,i=t.y-this._p.y,a=e.x-this._p.x,s=e.y-this._p.y,c=$f.signOfDet2x2(o,i,a,s);if(0===c)return this._isPointOnSegment=!0,null;s<i&&(c=-c),c>0&&this._crossingCount++}},Hf.prototype.isPointInPolygon=function(){return this.getLocation()!==yf.EXTERIOR},Hf.prototype.getLocation=function(){return this._isPointOnSegment?yf.BOUNDARY:this._crossingCount%2==1?yf.INTERIOR:yf.EXTERIOR},Hf.prototype.isOnSegment=function(){return this._isPointOnSegment},Hf.prototype.interfaces_=function(){return[]},Hf.prototype.getClass=function(){return Hf},Hf.locatePointInRing=function(){if(arguments[0]instanceof ff&&_f(arguments[1],Nf)){for(var t=arguments[0],e=arguments[1],n=new Hf(t),r=new ff,o=new ff,i=1;i<e.size();i++)if(e.getCoordinate(i,r),e.getCoordinate(i-1,o),n.countSegment(r,o),n.isOnSegment())return n.getLocation();return n.getLocation()}if(arguments[0]instanceof ff&&arguments[1]instanceof Array){for(var a=arguments[0],s=arguments[1],c=new Hf(a),u=1;u<s.length;u++){var l=s[u],d=s[u-1];if(c.countSegment(l,d),c.isOnSegment())return c.getLocation()}return c.getLocation()}};var Kf=function(){},Jf={CLOCKWISE:{configurable:!0},RIGHT:{configurable:!0},COUNTERCLOCKWISE:{configurable:!0},LEFT:{configurable:!0},COLLINEAR:{configurable:!0},STRAIGHT:{configurable:!0}};Kf.prototype.interfaces_=function(){return[]},Kf.prototype.getClass=function(){return Kf},Kf.orientationIndex=function(t,e,n){return Pf.orientationIndex(t,e,n)},Kf.signedArea=function(){if(arguments[0]instanceof Array){var t=arguments[0];if(t.length<3)return 0;for(var e=0,n=t[0].x,r=1;r<t.length-1;r++){var o=t[r].x-n,i=t[r+1].y;e+=o*(t[r-1].y-i)}return e/2}if(_f(arguments[0],Nf)){var a=arguments[0],s=a.size();if(s<3)return 0;var c=new ff,u=new ff,l=new ff;a.getCoordinate(0,u),a.getCoordinate(1,l);var d=u.x;l.x-=d;for(var p=0,f=1;f<s-1;f++)c.y=u.y,u.x=l.x,u.y=l.y,a.getCoordinate(f+1,l),l.x-=d,p+=u.x*(c.y-l.y);return p/2}},Kf.distanceLineLine=function(t,e,n,r){if(t.equals(e))return Kf.distancePointLine(t,n,r);if(n.equals(r))return Kf.distancePointLine(r,t,e);var o=!1;if(Rf.intersects(t,e,n,r)){var i=(e.x-t.x)*(r.y-n.y)-(e.y-t.y)*(r.x-n.x);if(0===i)o=!0;else{var a=(t.y-n.y)*(r.x-n.x)-(t.x-n.x)*(r.y-n.y),s=(t.y-n.y)*(e.x-t.x)-(t.x-n.x)*(e.y-t.y),c=s/i,u=a/i;(u<0||u>1||c<0||c>1)&&(o=!0)}}else o=!0;return o?bf.min(Kf.distancePointLine(t,n,r),Kf.distancePointLine(e,n,r),Kf.distancePointLine(n,t,e),Kf.distancePointLine(r,t,e)):0},Kf.isPointInRing=function(t,e){return Kf.locatePointInRing(t,e)!==yf.EXTERIOR},Kf.computeLength=function(t){var e=t.size();if(e<=1)return 0;var n=0,r=new ff;t.getCoordinate(0,r);for(var o=r.x,i=r.y,a=1;a<e;a++){t.getCoordinate(a,r);var s=r.x,c=r.y,u=s-o,l=c-i;n+=Math.sqrt(u*u+l*l),o=s,i=c}return n},Kf.isCCW=function(t){var e=t.length-1;if(e<3)throw new sf("Ring has fewer than 4 points, so orientation cannot be determined");for(var n=t[0],r=0,o=1;o<=e;o++){var i=t[o];i.y>n.y&&(n=i,r=o)}var a=r;do{(a-=1)<0&&(a=e)}while(t[a].equals2D(n)&&a!==r);var s=r;do{s=(s+1)%e}while(t[s].equals2D(n)&&s!==r);var c=t[a],u=t[s];if(c.equals2D(n)||u.equals2D(n)||c.equals2D(u))return!1;var l=Kf.computeOrientation(c,n,u),d=!1;return d=0===l?c.x>u.x:l>0,d},Kf.locatePointInRing=function(t,e){return Hf.locatePointInRing(t,e)},Kf.distancePointLinePerpendicular=function(t,e,n){var r=(n.x-e.x)*(n.x-e.x)+(n.y-e.y)*(n.y-e.y),o=((e.y-t.y)*(n.x-e.x)-(e.x-t.x)*(n.y-e.y))/r;return Math.abs(o)*Math.sqrt(r)},Kf.computeOrientation=function(t,e,n){return Kf.orientationIndex(t,e,n)},Kf.distancePointLine=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(0===e.length)throw new sf("Line array must contain at least one vertex");for(var n=t.distance(e[0]),r=0;r<e.length-1;r++){var o=Kf.distancePointLine(t,e[r],e[r+1]);o<n&&(n=o)}return n}if(3===arguments.length){var i=arguments[0],a=arguments[1],s=arguments[2];if(a.x===s.x&&a.y===s.y)return i.distance(a);var c=(s.x-a.x)*(s.x-a.x)+(s.y-a.y)*(s.y-a.y),u=((i.x-a.x)*(s.x-a.x)+(i.y-a.y)*(s.y-a.y))/c;if(u<=0)return i.distance(a);if(u>=1)return i.distance(s);var l=((a.y-i.y)*(s.x-a.x)-(a.x-i.x)*(s.y-a.y))/c;return Math.abs(l)*Math.sqrt(c)}},Kf.isOnLine=function(t,e){for(var n=new Wf,r=1;r<e.length;r++){var o=e[r-1],i=e[r];if(n.computeIntersection(t,o,i),n.hasIntersection())return!0}return!1},Jf.CLOCKWISE.get=function(){return-1},Jf.RIGHT.get=function(){return Kf.CLOCKWISE},Jf.COUNTERCLOCKWISE.get=function(){return 1},Jf.LEFT.get=function(){return Kf.COUNTERCLOCKWISE},Jf.COLLINEAR.get=function(){return 0},Jf.STRAIGHT.get=function(){return Kf.COLLINEAR},Object.defineProperties(Kf,Jf);var Zf=function(){};Zf.prototype.filter=function(t){},Zf.prototype.interfaces_=function(){return[]},Zf.prototype.getClass=function(){return Zf};var Qf=function(){var t=arguments[0];this._envelope=null,this._factory=null,this._SRID=null,this._userData=null,this._factory=t,this._SRID=t.getSRID()},th={serialVersionUID:{configurable:!0},SORTINDEX_POINT:{configurable:!0},SORTINDEX_MULTIPOINT:{configurable:!0},SORTINDEX_LINESTRING:{configurable:!0},SORTINDEX_LINEARRING:{configurable:!0},SORTINDEX_MULTILINESTRING:{configurable:!0},SORTINDEX_POLYGON:{configurable:!0},SORTINDEX_MULTIPOLYGON:{configurable:!0},SORTINDEX_GEOMETRYCOLLECTION:{configurable:!0},geometryChangedFilter:{configurable:!0}};Qf.prototype.isGeometryCollection=function(){return this.getSortIndex()===Qf.SORTINDEX_GEOMETRYCOLLECTION},Qf.prototype.getFactory=function(){return this._factory},Qf.prototype.getGeometryN=function(t){return this},Qf.prototype.getArea=function(){return 0},Qf.prototype.isRectangle=function(){return!1},Qf.prototype.equals=function(){if(arguments[0]instanceof Qf){var t=arguments[0];return null!==t&&this.equalsTopo(t)}if(arguments[0]instanceof Object){var e=arguments[0];if(!(e instanceof Qf))return!1;var n=e;return this.equalsExact(n)}},Qf.prototype.equalsExact=function(t){return this===t||this.equalsExact(t,0)},Qf.prototype.geometryChanged=function(){this.apply(Qf.geometryChangedFilter)},Qf.prototype.geometryChangedAction=function(){this._envelope=null},Qf.prototype.equalsNorm=function(t){return null!==t&&this.norm().equalsExact(t.norm())},Qf.prototype.getLength=function(){return 0},Qf.prototype.getNumGeometries=function(){return 1},Qf.prototype.compareTo=function(){if(1===arguments.length){var t=arguments[0],e=t;return this.getSortIndex()!==e.getSortIndex()?this.getSortIndex()-e.getSortIndex():this.isEmpty()&&e.isEmpty()?0:this.isEmpty()?-1:e.isEmpty()?1:this.compareToSameClass(t)}if(2===arguments.length){var n=arguments[0],r=arguments[1];return this.getSortIndex()!==n.getSortIndex()?this.getSortIndex()-n.getSortIndex():this.isEmpty()&&n.isEmpty()?0:this.isEmpty()?-1:n.isEmpty()?1:this.compareToSameClass(n,r)}},Qf.prototype.getUserData=function(){return this._userData},Qf.prototype.getSRID=function(){return this._SRID},Qf.prototype.getEnvelope=function(){return this.getFactory().toGeometry(this.getEnvelopeInternal())},Qf.prototype.checkNotGeometryCollection=function(t){if(t.getSortIndex()===Qf.SORTINDEX_GEOMETRYCOLLECTION)throw new sf("This method does not support GeometryCollection arguments")},Qf.prototype.equal=function(t,e,n){return 0===n?t.equals(e):t.distance(e)<=n},Qf.prototype.norm=function(){var t=this.copy();return t.normalize(),t},Qf.prototype.getPrecisionModel=function(){return this._factory.getPrecisionModel()},Qf.prototype.getEnvelopeInternal=function(){return null===this._envelope&&(this._envelope=this.computeEnvelopeInternal()),new Rf(this._envelope)},Qf.prototype.setSRID=function(t){this._SRID=t},Qf.prototype.setUserData=function(t){this._userData=t},Qf.prototype.compare=function(t,e){for(var n=t.iterator(),r=e.iterator();n.hasNext()&&r.hasNext();){var o=n.next(),i=r.next(),a=o.compareTo(i);if(0!==a)return a}return n.hasNext()?1:r.hasNext()?-1:0},Qf.prototype.hashCode=function(){return this.getEnvelopeInternal().hashCode()},Qf.prototype.isGeometryCollectionOrDerived=function(){return this.getSortIndex()===Qf.SORTINDEX_GEOMETRYCOLLECTION||this.getSortIndex()===Qf.SORTINDEX_MULTIPOINT||this.getSortIndex()===Qf.SORTINDEX_MULTILINESTRING||this.getSortIndex()===Qf.SORTINDEX_MULTIPOLYGON},Qf.prototype.interfaces_=function(){return[df,lf,ic]},Qf.prototype.getClass=function(){return Qf},Qf.hasNonEmptyElements=function(t){for(var e=0;e<t.length;e++)if(!t[e].isEmpty())return!0;return!1},Qf.hasNullElements=function(t){for(var e=0;e<t.length;e++)if(null===t[e])return!0;return!1},th.serialVersionUID.get=function(){return 0x799ea46522854c00},th.SORTINDEX_POINT.get=function(){return 0},th.SORTINDEX_MULTIPOINT.get=function(){return 1},th.SORTINDEX_LINESTRING.get=function(){return 2},th.SORTINDEX_LINEARRING.get=function(){return 3},th.SORTINDEX_MULTILINESTRING.get=function(){return 4},th.SORTINDEX_POLYGON.get=function(){return 5},th.SORTINDEX_MULTIPOLYGON.get=function(){return 6},th.SORTINDEX_GEOMETRYCOLLECTION.get=function(){return 7},th.geometryChangedFilter.get=function(){return eh},Object.defineProperties(Qf,th);var eh=function(){};eh.interfaces_=function(){return[Zf]},eh.filter=function(t){t.geometryChangedAction()};var nh=function(){};nh.prototype.filter=function(t){},nh.prototype.interfaces_=function(){return[]},nh.prototype.getClass=function(){return nh};var rh=function(){},oh={Mod2BoundaryNodeRule:{configurable:!0},EndPointBoundaryNodeRule:{configurable:!0},MultiValentEndPointBoundaryNodeRule:{configurable:!0},MonoValentEndPointBoundaryNodeRule:{configurable:!0},MOD2_BOUNDARY_RULE:{configurable:!0},ENDPOINT_BOUNDARY_RULE:{configurable:!0},MULTIVALENT_ENDPOINT_BOUNDARY_RULE:{configurable:!0},MONOVALENT_ENDPOINT_BOUNDARY_RULE:{configurable:!0},OGC_SFS_BOUNDARY_RULE:{configurable:!0}};rh.prototype.isInBoundary=function(t){},rh.prototype.interfaces_=function(){return[]},rh.prototype.getClass=function(){return rh},oh.Mod2BoundaryNodeRule.get=function(){return ih},oh.EndPointBoundaryNodeRule.get=function(){return ah},oh.MultiValentEndPointBoundaryNodeRule.get=function(){return sh},oh.MonoValentEndPointBoundaryNodeRule.get=function(){return ch},oh.MOD2_BOUNDARY_RULE.get=function(){return new ih},oh.ENDPOINT_BOUNDARY_RULE.get=function(){return new ah},oh.MULTIVALENT_ENDPOINT_BOUNDARY_RULE.get=function(){return new sh},oh.MONOVALENT_ENDPOINT_BOUNDARY_RULE.get=function(){return new ch},oh.OGC_SFS_BOUNDARY_RULE.get=function(){return rh.MOD2_BOUNDARY_RULE},Object.defineProperties(rh,oh);var ih=function(){};ih.prototype.isInBoundary=function(t){return t%2==1},ih.prototype.interfaces_=function(){return[rh]},ih.prototype.getClass=function(){return ih};var ah=function(){};ah.prototype.isInBoundary=function(t){return t>0},ah.prototype.interfaces_=function(){return[rh]},ah.prototype.getClass=function(){return ah};var sh=function(){};sh.prototype.isInBoundary=function(t){return t>1},sh.prototype.interfaces_=function(){return[rh]},sh.prototype.getClass=function(){return sh};var ch=function(){};ch.prototype.isInBoundary=function(t){return 1===t},ch.prototype.interfaces_=function(){return[rh]},ch.prototype.getClass=function(){return ch};var uh=function(){};uh.prototype.add=function(){},uh.prototype.addAll=function(){},uh.prototype.isEmpty=function(){},uh.prototype.iterator=function(){},uh.prototype.size=function(){},uh.prototype.toArray=function(){},uh.prototype.remove=function(){};var lh=function(t){function e(e){t.call(this),this.message=e||""}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={name:{configurable:!0}};return n.name.get=function(){return"IndexOutOfBoundsException"},Object.defineProperties(e,n),e}(Error),dh=function(){};dh.prototype.hasNext=function(){},dh.prototype.next=function(){},dh.prototype.remove=function(){};var ph=function(t){function e(){t.apply(this,arguments)}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.get=function(){},e.prototype.set=function(){},e.prototype.isEmpty=function(){},e}(uh);ac.prototype=new Error,ac.prototype.name="NoSuchElementException";var fh=function(t){function e(){t.call(this),this.array_=[],arguments[0]instanceof uh&&this.addAll(arguments[0])}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.ensureCapacity=function(){},e.prototype.interfaces_=function(){return[t,uh]},e.prototype.add=function(t){return 1===arguments.length?this.array_.push(t):this.array_.splice(arguments[0],arguments[1]),!0},e.prototype.clear=function(){this.array_=[]},e.prototype.addAll=function(t){for(var e=this,n=t.iterator();n.hasNext();)e.add(n.next());return!0},e.prototype.set=function(t,e){var n=this.array_[t];return this.array_[t]=e,n},e.prototype.iterator=function(){return new hh(this)},e.prototype.get=function(t){if(t<0||t>=this.size())throw new lh;return this.array_[t]},e.prototype.isEmpty=function(){return 0===this.array_.length},e.prototype.size=function(){return this.array_.length},e.prototype.toArray=function(){for(var t=this,e=[],n=0,r=this.array_.length;n<r;n++)e.push(t.array_[n]);return e},e.prototype.remove=function(t){for(var e=this,n=!1,r=0,o=this.array_.length;r<o;r++)if(e.array_[r]===t){e.array_.splice(r,1),n=!0;break}return n},e}(ph),hh=function(t){function e(e){t.call(this),this.arrayList_=e,this.position_=0}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.next=function(){if(this.position_===this.arrayList_.size())throw new ac;return this.arrayList_.get(this.position_++)},e.prototype.hasNext=function(){return this.position_<this.arrayList_.size()},e.prototype.set=function(t){return this.arrayList_.set(this.position_-1,t)},e.prototype.remove=function(){this.arrayList_.remove(this.arrayList_.get(this.position_))},e}(dh),gh=function(t){function e(){if(t.call(this),0===arguments.length);else if(1===arguments.length){var e=arguments[0];this.ensureCapacity(e.length),this.add(e,!0)}else if(2===arguments.length){var n=arguments[0],r=arguments[1];this.ensureCapacity(n.length),this.add(n,r)}}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={coordArrayType:{configurable:!0}};return n.coordArrayType.get=function(){return new Array(0).fill(null)},e.prototype.getCoordinate=function(t){return this.get(t)},e.prototype.addAll=function(){var e=this;if(2===arguments.length){for(var n=arguments[0],r=arguments[1],o=!1,i=n.iterator();i.hasNext();)e.add(i.next(),r),o=!0;return o}return t.prototype.addAll.apply(this,arguments)},e.prototype.clone=function(){for(var e=this,n=t.prototype.clone.call(this),r=0;r<this.size();r++)n.add(r,e.get(r).copy());return n},e.prototype.toCoordinateArray=function(){return this.toArray(e.coordArrayType)},e.prototype.add=function(){var e=this;if(1===arguments.length){var n=arguments[0];t.prototype.add.call(this,n)}else if(2===arguments.length){if(arguments[0]instanceof Array&&"boolean"==typeof arguments[1]){var r=arguments[0],o=arguments[1];return this.add(r,o,!0),!0}if(arguments[0]instanceof ff&&"boolean"==typeof arguments[1]){var i=arguments[0],a=arguments[1];if(!a&&this.size()>=1){var s=this.get(this.size()-1);if(s.equals2D(i))return null}t.prototype.add.call(this,i)}else if(arguments[0]instanceof Object&&"boolean"==typeof arguments[1]){var c=arguments[0],u=arguments[1];return this.add(c,u),!0}}else if(3===arguments.length){if("boolean"==typeof arguments[2]&&arguments[0]instanceof Array&&"boolean"==typeof arguments[1]){var l=arguments[0],d=arguments[1],p=arguments[2];if(p)for(var f=0;f<l.length;f++)e.add(l[f],d);else for(var h=l.length-1;h>=0;h--)e.add(l[h],d);return!0}if("boolean"==typeof arguments[2]&&Number.isInteger(arguments[0])&&arguments[1]instanceof ff){var g=arguments[0],m=arguments[1],y=arguments[2];if(!y){var v=this.size();if(v>0){if(g>0){var _=this.get(g-1);if(_.equals2D(m))return null}if(g<v){var b=this.get(g);if(b.equals2D(m))return null}}}t.prototype.add.call(this,g,m)}}else if(4===arguments.length){var w=arguments[0],x=arguments[1],E=arguments[2],C=arguments[3],S=1;E>C&&(S=-1);for(var I=E;I!==C;I+=S)e.add(w[I],x);return!0}},e.prototype.closeRing=function(){this.size()>0&&this.add(new ff(this.get(0)),!1)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},Object.defineProperties(e,n),e}(fh),mh=function(){},yh={ForwardComparator:{configurable:!0},BidirectionalComparator:{configurable:!0},coordArrayType:{configurable:!0}};yh.ForwardComparator.get=function(){return vh},yh.BidirectionalComparator.get=function(){return _h},yh.coordArrayType.get=function(){return new Array(0).fill(null)},mh.prototype.interfaces_=function(){return[]},mh.prototype.getClass=function(){return mh},mh.isRing=function(t){return!(t.length<4)&&!!t[0].equals2D(t[t.length-1])},mh.ptNotInList=function(t,e){for(var n=0;n<t.length;n++){var r=t[n];if(mh.indexOf(r,e)<0)return r}return null},mh.scroll=function(t,e){var n=mh.indexOf(e,t);if(n<0)return null;var r=new Array(t.length).fill(null);Of.arraycopy(t,n,r,0,t.length-n),Of.arraycopy(t,0,r,t.length-n,n),Of.arraycopy(r,0,t,0,t.length)},mh.equals=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];if(t===e)return!0;if(null===t||null===e)return!1;if(t.length!==e.length)return!1;for(var n=0;n<t.length;n++)if(!t[n].equals(e[n]))return!1;return!0}if(3===arguments.length){var r=arguments[0],o=arguments[1],i=arguments[2];if(r===o)return!0;if(null===r||null===o)return!1;if(r.length!==o.length)return!1;for(var a=0;a<r.length;a++)if(0!==i.compare(r[a],o[a]))return!1;return!0}},mh.intersection=function(t,e){for(var n=new gh,r=0;r<t.length;r++)e.intersects(t[r])&&n.add(t[r],!0);return n.toCoordinateArray()},mh.hasRepeatedPoints=function(t){for(var e=1;e<t.length;e++)if(t[e-1].equals(t[e]))return!0;return!1},mh.removeRepeatedPoints=function(t){return mh.hasRepeatedPoints(t)?new gh(t,!1).toCoordinateArray():t},mh.reverse=function(t){for(var e=t.length-1,n=Math.trunc(e/2),r=0;r<=n;r++){var o=t[r];t[r]=t[e-r],t[e-r]=o}},mh.removeNull=function(t){for(var e=0,n=0;n<t.length;n++)null!==t[n]&&e++;var r=new Array(e).fill(null);if(0===e)return r;for(var o=0,i=0;i<t.length;i++)null!==t[i]&&(r[o++]=t[i]);return r},mh.copyDeep=function(){if(1===arguments.length){for(var t=arguments[0],e=new Array(t.length).fill(null),n=0;n<t.length;n++)e[n]=new ff(t[n]);return e}if(5===arguments.length)for(var r=arguments[0],o=arguments[1],i=arguments[2],a=arguments[3],s=arguments[4],c=0;c<s;c++)i[a+c]=new ff(r[o+c])},mh.isEqualReversed=function(t,e){for(var n=0;n<t.length;n++){var r=t[n],o=e[t.length-n-1];if(0!==r.compareTo(o))return!1}return!0},mh.envelope=function(t){for(var e=new Rf,n=0;n<t.length;n++)e.expandToInclude(t[n]);return e},mh.toCoordinateArray=function(t){return t.toArray(mh.coordArrayType)},mh.atLeastNCoordinatesOrNothing=function(t,e){return e.length>=t?e:[]},mh.indexOf=function(t,e){for(var n=0;n<e.length;n++)if(t.equals(e[n]))return n;return-1},mh.increasingDirection=function(t){for(var e=0;e<Math.trunc(t.length/2);e++){var n=t.length-1-e,r=t[e].compareTo(t[n]);if(0!==r)return r}return 1},mh.compare=function(t,e){for(var n=0;n<t.length&&n<e.length;){var r=t[n].compareTo(e[n]);if(0!==r)return r;n++}return n<e.length?-1:n<t.length?1:0},mh.minCoordinate=function(t){for(var e=null,n=0;n<t.length;n++)(null===e||e.compareTo(t[n])>0)&&(e=t[n]);return e},mh.extract=function(t,e,n){e=bf.clamp(e,0,t.length),n=bf.clamp(n,-1,t.length);var r=n-e+1;n<0&&(r=0),e>=t.length&&(r=0),n<e&&(r=0);var o=new Array(r).fill(null);if(0===r)return o;for(var i=0,a=e;a<=n;a++)o[i++]=t[a];return o},Object.defineProperties(mh,yh);var vh=function(){}
;vh.prototype.compare=function(t,e){return mh.compare(t,e)},vh.prototype.interfaces_=function(){return[pf]},vh.prototype.getClass=function(){return vh};var _h=function(){};_h.prototype.compare=function(t,e){var n=t,r=e;if(n.length<r.length)return-1;if(n.length>r.length)return 1;if(0===n.length)return 0;var o=mh.compare(n,r);return mh.isEqualReversed(n,r)?0:o},_h.prototype.OLDcompare=function(t,e){var n=t,r=e;if(n.length<r.length)return-1;if(n.length>r.length)return 1;if(0===n.length)return 0;for(var o=mh.increasingDirection(n),i=mh.increasingDirection(r),a=o>0?0:n.length-1,s=i>0?0:n.length-1,c=0;c<n.length;c++){var u=n[a].compareTo(r[s]);if(0!==u)return u;a+=o,s+=i}return 0},_h.prototype.interfaces_=function(){return[pf]},_h.prototype.getClass=function(){return _h};var bh=function(){};bh.prototype.get=function(){},bh.prototype.put=function(){},bh.prototype.size=function(){},bh.prototype.values=function(){},bh.prototype.entrySet=function(){};var wh=function(t){function e(){t.apply(this,arguments)}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e}(bh);sc.prototype=new Error,sc.prototype.name="OperationNotSupported",cc.prototype=new uh,cc.prototype.contains=function(){};var xh=function(t){function e(){t.call(this),this.array_=[],arguments[0]instanceof uh&&this.addAll(arguments[0])}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.contains=function(t){for(var e=this,n=0,r=this.array_.length;n<r;n++){if(e.array_[n]===t)return!0}return!1},e.prototype.add=function(t){return!this.contains(t)&&(this.array_.push(t),!0)},e.prototype.addAll=function(t){for(var e=this,n=t.iterator();n.hasNext();)e.add(n.next());return!0},e.prototype.remove=function(t){throw new Error},e.prototype.size=function(){return this.array_.length},e.prototype.isEmpty=function(){return 0===this.array_.length},e.prototype.toArray=function(){for(var t=this,e=[],n=0,r=this.array_.length;n<r;n++)e.push(t.array_[n]);return e},e.prototype.iterator=function(){return new Eh(this)},e}(cc),Eh=function(t){function e(e){t.call(this),this.hashSet_=e,this.position_=0}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.next=function(){if(this.position_===this.hashSet_.size())throw new ac;return this.hashSet_.array_[this.position_++]},e.prototype.hasNext=function(){return this.position_<this.hashSet_.size()},e.prototype.remove=function(){throw new sc},e}(dh),Ch=0;hc.prototype=new wh,hc.prototype.get=function(t){for(var e=this.root_;null!==e;){var n=t.compareTo(e.key);if(n<0)e=e.left;else{if(!(n>0))return e.value;e=e.right}}return null},hc.prototype.put=function(t,e){if(null===this.root_)return this.root_={key:t,value:e,left:null,right:null,parent:null,color:Ch,getValue:function(){return this.value},getKey:function(){return this.key}},this.size_=1,null;var n,r,o=this.root_;do{if(n=o,(r=t.compareTo(o.key))<0)o=o.left;else{if(!(r>0)){var i=o.value;return o.value=e,i}o=o.right}}while(null!==o);var a={key:t,left:null,right:null,value:e,parent:n,color:Ch,getValue:function(){return this.value},getKey:function(){return this.key}};return r<0?n.left=a:n.right=a,this.fixAfterInsertion(a),this.size_++,null},hc.prototype.fixAfterInsertion=function(t){var e=this;for(t.color=1;null!=t&&t!==this.root_&&1===t.parent.color;)if(lc(t)===pc(lc(lc(t)))){var n=fc(lc(lc(t)));1===uc(n)?(dc(lc(t),Ch),dc(n,Ch),dc(lc(lc(t)),1),t=lc(lc(t))):(t===fc(lc(t))&&(t=lc(t),e.rotateLeft(t)),dc(lc(t),Ch),dc(lc(lc(t)),1),e.rotateRight(lc(lc(t))))}else{var r=pc(lc(lc(t)));1===uc(r)?(dc(lc(t),Ch),dc(r,Ch),dc(lc(lc(t)),1),t=lc(lc(t))):(t===pc(lc(t))&&(t=lc(t),e.rotateRight(t)),dc(lc(t),Ch),dc(lc(lc(t)),1),e.rotateLeft(lc(lc(t))))}this.root_.color=Ch},hc.prototype.values=function(){var t=new fh,e=this.getFirstEntry();if(null!==e)for(t.add(e.value);null!==(e=hc.successor(e));)t.add(e.value);return t},hc.prototype.entrySet=function(){var t=new xh,e=this.getFirstEntry();if(null!==e)for(t.add(e);null!==(e=hc.successor(e));)t.add(e);return t},hc.prototype.rotateLeft=function(t){if(null!=t){var e=t.right;t.right=e.left,null!=e.left&&(e.left.parent=t),e.parent=t.parent,null===t.parent?this.root_=e:t.parent.left===t?t.parent.left=e:t.parent.right=e,e.left=t,t.parent=e}},hc.prototype.rotateRight=function(t){if(null!=t){var e=t.left;t.left=e.right,null!=e.right&&(e.right.parent=t),e.parent=t.parent,null===t.parent?this.root_=e:t.parent.right===t?t.parent.right=e:t.parent.left=e,e.right=t,t.parent=e}},hc.prototype.getFirstEntry=function(){var t=this.root_;if(null!=t)for(;null!=t.left;)t=t.left;return t},hc.successor=function(t){if(null===t)return null;if(null!==t.right){for(var e=t.right;null!==e.left;)e=e.left;return e}for(var n=t.parent,r=t;null!==n&&r===n.right;)r=n,n=n.parent;return n},hc.prototype.size=function(){return this.size_};var Sh=function(){};Sh.prototype.interfaces_=function(){return[]},Sh.prototype.getClass=function(){return Sh},gc.prototype=new cc,mc.prototype=new gc,mc.prototype.contains=function(t){for(var e=this,n=0,r=this.array_.length;n<r;n++){if(0===e.array_[n].compareTo(t))return!0}return!1},mc.prototype.add=function(t){var e=this;if(this.contains(t))return!1;for(var n=0,r=this.array_.length;n<r;n++){if(1===e.array_[n].compareTo(t))return e.array_.splice(n,0,t),!0}return this.array_.push(t),!0},mc.prototype.addAll=function(t){for(var e=this,n=t.iterator();n.hasNext();)e.add(n.next());return!0},mc.prototype.remove=function(t){throw new sc},mc.prototype.size=function(){return this.array_.length},mc.prototype.isEmpty=function(){return 0===this.array_.length},mc.prototype.toArray=function(){for(var t=this,e=[],n=0,r=this.array_.length;n<r;n++)e.push(t.array_[n]);return e},mc.prototype.iterator=function(){return new Ih(this)};var Ih=function(t){this.treeSet_=t,this.position_=0};Ih.prototype.next=function(){if(this.position_===this.treeSet_.size())throw new ac;return this.treeSet_.array_[this.position_++]},Ih.prototype.hasNext=function(){return this.position_<this.treeSet_.size()},Ih.prototype.remove=function(){throw new sc};var Ph=function(){};Ph.sort=function(){var t,e,n,r,o=arguments[0];if(1===arguments.length)r=function(t,e){return t.compareTo(e)},o.sort(r);else if(2===arguments.length)n=arguments[1],r=function(t,e){return n.compare(t,e)},o.sort(r);else if(3===arguments.length){e=o.slice(arguments[1],arguments[2]),e.sort();var i=o.slice(0,arguments[1]).concat(e,o.slice(arguments[2],o.length));for(o.splice(0,o.length),t=0;t<i.length;t++)o.push(i[t])}else if(4===arguments.length)for(e=o.slice(arguments[1],arguments[2]),n=arguments[3],r=function(t,e){return n.compare(t,e)},e.sort(r),i=o.slice(0,arguments[1]).concat(e,o.slice(arguments[2],o.length)),o.splice(0,o.length),t=0;t<i.length;t++)o.push(i[t])},Ph.asList=function(t){for(var e=new fh,n=0,r=t.length;n<r;n++)e.add(t[n]);return e};var Ah=function(){},Nh={P:{configurable:!0},L:{configurable:!0},A:{configurable:!0},FALSE:{configurable:!0},TRUE:{configurable:!0},DONTCARE:{configurable:!0},SYM_FALSE:{configurable:!0},SYM_TRUE:{configurable:!0},SYM_DONTCARE:{configurable:!0},SYM_P:{configurable:!0},SYM_L:{configurable:!0},SYM_A:{configurable:!0}};Nh.P.get=function(){return 0},Nh.L.get=function(){return 1},Nh.A.get=function(){return 2},Nh.FALSE.get=function(){return-1},Nh.TRUE.get=function(){return-2},Nh.DONTCARE.get=function(){return-3},Nh.SYM_FALSE.get=function(){return"F"},Nh.SYM_TRUE.get=function(){return"T"},Nh.SYM_DONTCARE.get=function(){return"*"},Nh.SYM_P.get=function(){return"0"},Nh.SYM_L.get=function(){return"1"},Nh.SYM_A.get=function(){return"2"},Ah.prototype.interfaces_=function(){return[]},Ah.prototype.getClass=function(){return Ah},Ah.toDimensionSymbol=function(t){switch(t){case Ah.FALSE:return Ah.SYM_FALSE;case Ah.TRUE:return Ah.SYM_TRUE;case Ah.DONTCARE:return Ah.SYM_DONTCARE;case Ah.P:return Ah.SYM_P;case Ah.L:return Ah.SYM_L;case Ah.A:return Ah.SYM_A}throw new sf("Unknown dimension value: "+t)},Ah.toDimensionValue=function(t){switch(Cf.toUpperCase(t)){case Ah.SYM_FALSE:return Ah.FALSE;case Ah.SYM_TRUE:return Ah.TRUE;case Ah.SYM_DONTCARE:return Ah.DONTCARE;case Ah.SYM_P:return Ah.P;case Ah.SYM_L:return Ah.L;case Ah.SYM_A:return Ah.A}throw new sf("Unknown dimension symbol: "+t)},Object.defineProperties(Ah,Nh);var Mh=function(){};Mh.prototype.filter=function(t){},Mh.prototype.interfaces_=function(){return[]},Mh.prototype.getClass=function(){return Mh};var Lh=function(){};Lh.prototype.filter=function(t,e){},Lh.prototype.isDone=function(){},Lh.prototype.isGeometryChanged=function(){},Lh.prototype.interfaces_=function(){return[]},Lh.prototype.getClass=function(){return Lh};var Dh=function(t){function e(e,n){if(t.call(this,n),this._geometries=e||[],t.hasNullElements(this._geometries))throw new sf("geometries must not contain null elements")}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={serialVersionUID:{configurable:!0}};return e.prototype.computeEnvelopeInternal=function(){for(var t=this,e=new Rf,n=0;n<this._geometries.length;n++)e.expandToInclude(t._geometries[n].getEnvelopeInternal());return e},e.prototype.getGeometryN=function(t){return this._geometries[t]},e.prototype.getSortIndex=function(){return t.SORTINDEX_GEOMETRYCOLLECTION},e.prototype.getCoordinates=function(){for(var t=this,e=new Array(this.getNumPoints()).fill(null),n=-1,r=0;r<this._geometries.length;r++)for(var o=t._geometries[r].getCoordinates(),i=0;i<o.length;i++)n++,e[n]=o[i];return e},e.prototype.getArea=function(){for(var t=this,e=0,n=0;n<this._geometries.length;n++)e+=t._geometries[n].getArea();return e},e.prototype.equalsExact=function(){var e=this;if(2===arguments.length){var n=arguments[0],r=arguments[1];if(!this.isEquivalentClass(n))return!1;var o=n;if(this._geometries.length!==o._geometries.length)return!1;for(var i=0;i<this._geometries.length;i++)if(!e._geometries[i].equalsExact(o._geometries[i],r))return!1;return!0}return t.prototype.equalsExact.apply(this,arguments)},e.prototype.normalize=function(){for(var t=this,e=0;e<this._geometries.length;e++)t._geometries[e].normalize();Ph.sort(this._geometries)},e.prototype.getCoordinate=function(){return this.isEmpty()?null:this._geometries[0].getCoordinate()},e.prototype.getBoundaryDimension=function(){for(var t=this,e=Ah.FALSE,n=0;n<this._geometries.length;n++)e=Math.max(e,t._geometries[n].getBoundaryDimension());return e},e.prototype.getDimension=function(){for(var t=this,e=Ah.FALSE,n=0;n<this._geometries.length;n++)e=Math.max(e,t._geometries[n].getDimension());return e},e.prototype.getLength=function(){for(var t=this,e=0,n=0;n<this._geometries.length;n++)e+=t._geometries[n].getLength();return e},e.prototype.getNumPoints=function(){for(var t=this,e=0,n=0;n<this._geometries.length;n++)e+=t._geometries[n].getNumPoints();return e},e.prototype.getNumGeometries=function(){return this._geometries.length},e.prototype.reverse=function(){for(var t=this,e=this._geometries.length,n=new Array(e).fill(null),r=0;r<this._geometries.length;r++)n[r]=t._geometries[r].reverse();return this.getFactory().createGeometryCollection(n)},e.prototype.compareToSameClass=function(){var t=this;if(1===arguments.length){var e=arguments[0],n=new mc(Ph.asList(this._geometries)),r=new mc(Ph.asList(e._geometries));return this.compare(n,r)}if(2===arguments.length){for(var o=arguments[0],i=arguments[1],a=o,s=this.getNumGeometries(),c=a.getNumGeometries(),u=0;u<s&&u<c;){var l=t.getGeometryN(u),d=a.getGeometryN(u),p=l.compareToSameClass(d,i);if(0!==p)return p;u++}return u<s?1:u<c?-1:0}},e.prototype.apply=function(){var t=this;if(_f(arguments[0],nh))for(var e=arguments[0],n=0;n<this._geometries.length;n++)t._geometries[n].apply(e);else if(_f(arguments[0],Lh)){var r=arguments[0];if(0===this._geometries.length)return null;for(var o=0;o<this._geometries.length&&(t._geometries[o].apply(r),!r.isDone());o++);r.isGeometryChanged()&&this.geometryChanged()}else if(_f(arguments[0],Mh)){var i=arguments[0];i.filter(this);for(var a=0;a<this._geometries.length;a++)t._geometries[a].apply(i)}else if(_f(arguments[0],Zf)){var s=arguments[0];s.filter(this);for(var c=0;c<this._geometries.length;c++)t._geometries[c].apply(s)}},e.prototype.getBoundary=function(){return this.checkNotGeometryCollection(this),jf.shouldNeverReachHere(),null},e.prototype.clone=function(){var e=this,n=t.prototype.clone.call(this);n._geometries=new Array(this._geometries.length).fill(null);for(var r=0;r<this._geometries.length;r++)n._geometries[r]=e._geometries[r].clone();return n},e.prototype.getGeometryType=function(){return"GeometryCollection"},e.prototype.copy=function(){for(var t=this,n=new Array(this._geometries.length).fill(null),r=0;r<n.length;r++)n[r]=t._geometries[r].copy();return new e(n,this._factory)},e.prototype.isEmpty=function(){for(var t=this,e=0;e<this._geometries.length;e++)if(!t._geometries[e].isEmpty())return!1;return!0},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},n.serialVersionUID.get=function(){return-0x4f07bcb1f857d800},Object.defineProperties(e,n),e}(Qf),Oh=function(t){function e(){t.apply(this,arguments)}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={serialVersionUID:{configurable:!0}};return e.prototype.getSortIndex=function(){return Qf.SORTINDEX_MULTILINESTRING},e.prototype.equalsExact=function(){if(2===arguments.length){var e=arguments[0],n=arguments[1];return!!this.isEquivalentClass(e)&&t.prototype.equalsExact.call(this,e,n)}return t.prototype.equalsExact.apply(this,arguments)},e.prototype.getBoundaryDimension=function(){return this.isClosed()?Ah.FALSE:0},e.prototype.isClosed=function(){var t=this;if(this.isEmpty())return!1;for(var e=0;e<this._geometries.length;e++)if(!t._geometries[e].isClosed())return!1;return!0},e.prototype.getDimension=function(){return 1},e.prototype.reverse=function(){for(var t=this,e=this._geometries.length,n=new Array(e).fill(null),r=0;r<this._geometries.length;r++)n[e-1-r]=t._geometries[r].reverse();return this.getFactory().createMultiLineString(n)},e.prototype.getBoundary=function(){return new Th(this).getBoundary()},e.prototype.getGeometryType=function(){return"MultiLineString"},e.prototype.copy=function(){for(var t=this,n=new Array(this._geometries.length).fill(null),r=0;r<n.length;r++)n[r]=t._geometries[r].copy();return new e(n,this._factory)},e.prototype.interfaces_=function(){return[Sh]},e.prototype.getClass=function(){return e},n.serialVersionUID.get=function(){return 0x7155d2ab4afa8000},Object.defineProperties(e,n),e}(Dh),Th=function(){if(this._geom=null,this._geomFact=null,this._bnRule=null,this._endpointMap=null,1===arguments.length){var t=arguments[0],e=rh.MOD2_BOUNDARY_RULE;this._geom=t,this._geomFact=t.getFactory(),this._bnRule=e}else if(2===arguments.length){var n=arguments[0],r=arguments[1];this._geom=n,this._geomFact=n.getFactory(),this._bnRule=r}};Th.prototype.boundaryMultiLineString=function(t){if(this._geom.isEmpty())return this.getEmptyMultiPoint();var e=this.computeBoundaryCoordinates(t);return 1===e.length?this._geomFact.createPoint(e[0]):this._geomFact.createMultiPointFromCoords(e)},Th.prototype.getBoundary=function(){return this._geom instanceof Uh?this.boundaryLineString(this._geom):this._geom instanceof Oh?this.boundaryMultiLineString(this._geom):this._geom.getBoundary()},Th.prototype.boundaryLineString=function(t){if(this._geom.isEmpty())return this.getEmptyMultiPoint();if(t.isClosed()){return this._bnRule.isInBoundary(2)?t.getStartPoint():this._geomFact.createMultiPoint()}return this._geomFact.createMultiPoint([t.getStartPoint(),t.getEndPoint()])},Th.prototype.getEmptyMultiPoint=function(){return this._geomFact.createMultiPoint()},Th.prototype.computeBoundaryCoordinates=function(t){var e=this,n=new fh;this._endpointMap=new hc;for(var r=0;r<t.getNumGeometries();r++){var o=t.getGeometryN(r);0!==o.getNumPoints()&&(e.addEndpoint(o.getCoordinateN(0)),e.addEndpoint(o.getCoordinateN(o.getNumPoints()-1)))}for(var i=this._endpointMap.entrySet().iterator();i.hasNext();){var a=i.next(),s=a.getValue(),c=s.count;e._bnRule.isInBoundary(c)&&n.add(a.getKey())}return mh.toCoordinateArray(n)},Th.prototype.addEndpoint=function(t){var e=this._endpointMap.get(t);null===e&&(e=new Rh,this._endpointMap.put(t,e)),e.count++},Th.prototype.interfaces_=function(){return[]},Th.prototype.getClass=function(){return Th},Th.getBoundary=function(){if(1===arguments.length){var t=arguments[0];return new Th(t).getBoundary()}if(2===arguments.length){var e=arguments[0],n=arguments[1];return new Th(e,n).getBoundary()}};var Rh=function(){this.count=null};Rh.prototype.interfaces_=function(){return[]},Rh.prototype.getClass=function(){return Rh};var Fh=function(){},Gh=function(){},qh={NEWLINE:{configurable:!0},SIMPLE_ORDINATE_FORMAT:{configurable:!0}};Gh.prototype.interfaces_=function(){return[]},Gh.prototype.getClass=function(){return Gh},Gh.chars=function(t,e){for(var n=new Array(e).fill(null),r=0;r<e;r++)n[r]=t;return String(n)},Gh.getStackTrace=function(){if(1===arguments.length){var t=arguments[0],e=new _c,n=new yc(e);return t.printStackTrace(n),e.toString()}if(2===arguments.length){for(var r=arguments[0],o=arguments[1],i="",a=new vc(Gh.getStackTrace(r)),s=new wc(a),c=0;c<o;c++)try{i+=s.readLine()+Gh.NEWLINE}catch(t){if(!(t instanceof bc))throw t;jf.shouldNeverReachHere()}return i}},Gh.split=function(t,e){for(var n=e.length,r=new fh,o=""+t,i=o.indexOf(e);i>=0;){var a=o.substring(0,i);r.add(a),o=o.substring(i+n),i=o.indexOf(e)}o.length>0&&r.add(o);for(var s=new Array(r.size()).fill(null),c=0;c<s.length;c++)s[c]=r.get(c);return s},Gh.toString=function(){if(1===arguments.length){var t=arguments[0];return Gh.SIMPLE_ORDINATE_FORMAT.format(t)}},Gh.spaces=function(t){return Gh.chars(" ",t)},qh.NEWLINE.get=function(){return Of.getProperty("line.separator")},qh.SIMPLE_ORDINATE_FORMAT.get=function(){return new Fh("0.#")},Object.defineProperties(Gh,qh);var kh=function(){};kh.prototype.interfaces_=function(){return[]},kh.prototype.getClass=function(){return kh},kh.copyCoord=function(t,e,n,r){for(var o=Math.min(t.getDimension(),n.getDimension()),i=0;i<o;i++)n.setOrdinate(r,i,t.getOrdinate(e,i))},kh.isRing=function(t){var e=t.size();return 0===e||!(e<=3)&&(t.getOrdinate(0,Nf.X)===t.getOrdinate(e-1,Nf.X)&&t.getOrdinate(0,Nf.Y)===t.getOrdinate(e-1,Nf.Y))},kh.isEqual=function(t,e){var n=t.size();if(n!==e.size())return!1;for(var r=Math.min(t.getDimension(),e.getDimension()),o=0;o<n;o++)for(var i=0;i<r;i++){var a=t.getOrdinate(o,i),s=e.getOrdinate(o,i);if(t.getOrdinate(o,i)!==e.getOrdinate(o,i)&&(!cf.isNaN(a)||!cf.isNaN(s)))return!1}return!0},kh.extend=function(t,e,n){var r=t.create(n,e.getDimension()),o=e.size();if(kh.copy(e,0,r,0,o),o>0)for(var i=o;i<n;i++)kh.copy(e,o-1,r,i,1);return r},kh.reverse=function(t){for(var e=t.size()-1,n=Math.trunc(e/2),r=0;r<=n;r++)kh.swap(t,r,e-r)},kh.swap=function(t,e,n){if(e===n)return null;for(var r=0;r<t.getDimension();r++){var o=t.getOrdinate(e,r);t.setOrdinate(e,r,t.getOrdinate(n,r)),t.setOrdinate(n,r,o)}},kh.copy=function(t,e,n,r,o){for(var i=0;i<o;i++)kh.copyCoord(t,e+i,n,r+i)},kh.toString=function(){if(1===arguments.length){var t=arguments[0],e=t.size();if(0===e)return"()";var n=t.getDimension(),r=new xf;r.append("(");for(var o=0;o<e;o++){o>0&&r.append(" ");for(var i=0;i<n;i++)i>0&&r.append(","),r.append(Gh.toString(t.getOrdinate(o,i)))}return r.append(")"),r.toString()}},kh.ensureValidRing=function(t,e){var n=e.size();return 0===n?e:n<=3?kh.createClosedRing(t,e,4):e.getOrdinate(0,Nf.X)===e.getOrdinate(n-1,Nf.X)&&e.getOrdinate(0,Nf.Y)===e.getOrdinate(n-1,Nf.Y)?e:kh.createClosedRing(t,e,n+1)},kh.createClosedRing=function(t,e,n){var r=t.create(n,e.getDimension()),o=e.size();kh.copy(e,0,r,0,o);for(var i=o;i<n;i++)kh.copy(e,0,r,i,1);return r};var Uh=function(t){function e(e,n){t.call(this,n),this._points=null,this.init(e)}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={serialVersionUID:{configurable:!0}};return e.prototype.computeEnvelopeInternal=function(){return this.isEmpty()?new Rf:this._points.expandEnvelope(new Rf)},e.prototype.isRing=function(){return this.isClosed()&&this.isSimple()},e.prototype.getSortIndex=function(){return t.SORTINDEX_LINESTRING},e.prototype.getCoordinates=function(){return this._points.toCoordinateArray()},e.prototype.equalsExact=function(){var e=this;if(2===arguments.length){var n=arguments[0],r=arguments[1];if(!this.isEquivalentClass(n))return!1;var o=n;if(this._points.size()!==o._points.size())return!1;for(var i=0;i<this._points.size();i++)if(!e.equal(e._points.getCoordinate(i),o._points.getCoordinate(i),r))return!1;return!0}return t.prototype.equalsExact.apply(this,arguments)},e.prototype.normalize=function(){for(var t=this,e=0;e<Math.trunc(this._points.size()/2);e++){var n=t._points.size()-1-e;if(!t._points.getCoordinate(e).equals(t._points.getCoordinate(n)))return t._points.getCoordinate(e).compareTo(t._points.getCoordinate(n))>0&&kh.reverse(t._points),null}},e.prototype.getCoordinate=function(){return this.isEmpty()?null:this._points.getCoordinate(0)},e.prototype.getBoundaryDimension=function(){return this.isClosed()?Ah.FALSE:0},e.prototype.isClosed=function(){return!this.isEmpty()&&this.getCoordinateN(0).equals2D(this.getCoordinateN(this.getNumPoints()-1))},e.prototype.getEndPoint=function(){return this.isEmpty()?null:this.getPointN(this.getNumPoints()-1)},e.prototype.getDimension=function(){return 1},e.prototype.getLength=function(){return Kf.computeLength(this._points)},e.prototype.getNumPoints=function(){return this._points.size()},e.prototype.reverse=function(){var t=this._points.copy();return kh.reverse(t),this.getFactory().createLineString(t)},e.prototype.compareToSameClass=function(){var t=this;if(1===arguments.length){for(var e=arguments[0],n=e,r=0,o=0;r<this._points.size()&&o<n._points.size();){var i=t._points.getCoordinate(r).compareTo(n._points.getCoordinate(o));if(0!==i)return i;r++,o++}return r<this._points.size()?1:o<n._points.size()?-1:0}if(2===arguments.length){var a=arguments[0],s=arguments[1],c=a;return s.compare(this._points,c._points)}},e.prototype.apply=function(){var t=this;if(_f(arguments[0],nh))for(var e=arguments[0],n=0;n<this._points.size();n++)e.filter(t._points.getCoordinate(n));else if(_f(arguments[0],Lh)){var r=arguments[0];if(0===this._points.size())return null;for(var o=0;o<this._points.size()&&(r.filter(t._points,o),!r.isDone());o++);r.isGeometryChanged()&&this.geometryChanged()}else if(_f(arguments[0],Mh)){var i=arguments[0];i.filter(this)}else if(_f(arguments[0],Zf)){var a=arguments[0];a.filter(this)}},e.prototype.getBoundary=function(){return new Th(this).getBoundary()},e.prototype.isEquivalentClass=function(t){return t instanceof e},e.prototype.clone=function(){var e=t.prototype.clone.call(this);return e._points=this._points.clone(),e},e.prototype.getCoordinateN=function(t){return this._points.getCoordinate(t)},e.prototype.getGeometryType=function(){return"LineString"},e.prototype.copy=function(){return new e(this._points.copy(),this._factory)},e.prototype.getCoordinateSequence=function(){return this._points},e.prototype.isEmpty=function(){return 0===this._points.size()},e.prototype.init=function(t){if(null===t&&(t=this.getFactory().getCoordinateSequenceFactory().create([])),1===t.size())throw new sf("Invalid number of points in LineString (found "+t.size()+" - must be 0 or >= 2)");this._points=t},e.prototype.isCoordinate=function(t){for(var e=this,n=0;n<this._points.size();n++)if(e._points.getCoordinate(n).equals(t))return!0;return!1},e.prototype.getStartPoint=function(){return this.isEmpty()?null:this.getPointN(0)},e.prototype.getPointN=function(t){return this.getFactory().createPoint(this._points.getCoordinate(t))},e.prototype.interfaces_=function(){return[Sh]},e.prototype.getClass=function(){return e},n.serialVersionUID.get=function(){return 0x2b2b51ba435c8e00},Object.defineProperties(e,n),e}(Qf),Bh=function(){};Bh.prototype.interfaces_=function(){return[]},Bh.prototype.getClass=function(){return Bh};var Vh=function(t){function e(e,n){t.call(this,n),this._coordinates=e||null,this.init(this._coordinates)}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={serialVersionUID:{configurable:!0}};return e.prototype.computeEnvelopeInternal=function(){if(this.isEmpty())return new Rf;var t=new Rf;return t.expandToInclude(this._coordinates.getX(0),this._coordinates.getY(0)),t},e.prototype.getSortIndex=function(){return t.SORTINDEX_POINT},e.prototype.getCoordinates=function(){return this.isEmpty()?[]:[this.getCoordinate()]},e.prototype.equalsExact=function(){if(2===arguments.length){var e=arguments[0],n=arguments[1];return!!this.isEquivalentClass(e)&&(!(!this.isEmpty()||!e.isEmpty())||this.isEmpty()===e.isEmpty()&&this.equal(e.getCoordinate(),this.getCoordinate(),n))}return t.prototype.equalsExact.apply(this,arguments)},e.prototype.normalize=function(){},e.prototype.getCoordinate=function(){return 0!==this._coordinates.size()?this._coordinates.getCoordinate(0):null},e.prototype.getBoundaryDimension=function(){return Ah.FALSE},e.prototype.getDimension=function(){return 0},e.prototype.getNumPoints=function(){return this.isEmpty()?0:1},e.prototype.reverse=function(){return this.copy()},e.prototype.getX=function(){if(null===this.getCoordinate())throw new Error("getX called on empty Point");return this.getCoordinate().x},e.prototype.compareToSameClass=function(){if(1===arguments.length){var t=arguments[0],e=t;return this.getCoordinate().compareTo(e.getCoordinate())}if(2===arguments.length){var n=arguments[0],r=arguments[1],o=n;return r.compare(this._coordinates,o._coordinates)}},e.prototype.apply=function(){if(_f(arguments[0],nh)){var t=arguments[0];if(this.isEmpty())return null;t.filter(this.getCoordinate())}else if(_f(arguments[0],Lh)){var e=arguments[0];if(this.isEmpty())return null;e.filter(this._coordinates,0),e.isGeometryChanged()&&this.geometryChanged()}else if(_f(arguments[0],Mh)){var n=arguments[0];n.filter(this)}else if(_f(arguments[0],Zf)){var r=arguments[0];r.filter(this)}},e.prototype.getBoundary=function(){return this.getFactory().createGeometryCollection(null)},e.prototype.clone=function(){var e=t.prototype.clone.call(this);return e._coordinates=this._coordinates.clone(),e},e.prototype.getGeometryType=function(){return"Point"},e.prototype.copy=function(){return new e(this._coordinates.copy(),this._factory)},e.prototype.getCoordinateSequence=function(){return this._coordinates},e.prototype.getY=function(){if(null===this.getCoordinate())throw new Error("getY called on empty Point");return this.getCoordinate().y},e.prototype.isEmpty=function(){return 0===this._coordinates.size()},e.prototype.init=function(t){null===t&&(t=this.getFactory().getCoordinateSequenceFactory().create([])),jf.isTrue(t.size()<=1),this._coordinates=t},e.prototype.isSimple=function(){return!0},e.prototype.interfaces_=function(){return[Bh]},e.prototype.getClass=function(){return e},n.serialVersionUID.get=function(){return 0x44077bad161cbc00},Object.defineProperties(e,n),e}(Qf),zh=function(){};zh.prototype.interfaces_=function(){return[]},zh.prototype.getClass=function(){return zh};var jh=function(t){function e(e,n,r){if(t.call(this,r),this._shell=null,this._holes=null,null===e&&(e=this.getFactory().createLinearRing()),null===n&&(n=[]),t.hasNullElements(n))throw new sf("holes must not contain null elements");if(e.isEmpty()&&t.hasNonEmptyElements(n))throw new sf("shell is empty but holes are not");this._shell=e,this._holes=n}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={serialVersionUID:{configurable:!0}};return e.prototype.computeEnvelopeInternal=function(){return this._shell.getEnvelopeInternal()},e.prototype.getSortIndex=function(){return t.SORTINDEX_POLYGON},e.prototype.getCoordinates=function(){var t=this;if(this.isEmpty())return[];for(var e=new Array(this.getNumPoints()).fill(null),n=-1,r=this._shell.getCoordinates(),o=0;o<r.length;o++)n++,e[n]=r[o];for(var i=0;i<this._holes.length;i++)for(var a=t._holes[i].getCoordinates(),s=0;s<a.length;s++)n++,e[n]=a[s];return e},e.prototype.getArea=function(){var t=this,e=0;e+=Math.abs(Kf.signedArea(this._shell.getCoordinateSequence()));for(var n=0;n<this._holes.length;n++)e-=Math.abs(Kf.signedArea(t._holes[n].getCoordinateSequence()));return e},e.prototype.isRectangle=function(){if(0!==this.getNumInteriorRing())return!1;if(null===this._shell)return!1;if(5!==this._shell.getNumPoints())return!1;for(var t=this._shell.getCoordinateSequence(),e=this.getEnvelopeInternal(),n=0;n<5;n++){var r=t.getX(n);if(r!==e.getMinX()&&r!==e.getMaxX())return!1;var o=t.getY(n);if(o!==e.getMinY()&&o!==e.getMaxY())return!1}for(var i=t.getX(0),a=t.getY(0),s=1;s<=4;s++){var c=t.getX(s),u=t.getY(s);if(c!==i===(u!==a))return!1;i=c,a=u}return!0},e.prototype.equalsExact=function(){var e=this;if(2===arguments.length){var n=arguments[0],r=arguments[1];if(!this.isEquivalentClass(n))return!1;var o=n,i=this._shell,a=o._shell;if(!i.equalsExact(a,r))return!1;if(this._holes.length!==o._holes.length)return!1;for(var s=0;s<this._holes.length;s++)if(!e._holes[s].equalsExact(o._holes[s],r))return!1;return!0}return t.prototype.equalsExact.apply(this,arguments)},e.prototype.normalize=function(){var t=this;if(0===arguments.length){this.normalize(this._shell,!0);for(var e=0;e<this._holes.length;e++)t.normalize(t._holes[e],!1);Ph.sort(this._holes)}else if(2===arguments.length){var n=arguments[0],r=arguments[1];if(n.isEmpty())return null;var o=new Array(n.getCoordinates().length-1).fill(null);Of.arraycopy(n.getCoordinates(),0,o,0,o.length);var i=mh.minCoordinate(n.getCoordinates());mh.scroll(o,i),Of.arraycopy(o,0,n.getCoordinates(),0,o.length),n.getCoordinates()[o.length]=o[0],Kf.isCCW(n.getCoordinates())===r&&mh.reverse(n.getCoordinates())}},e.prototype.getCoordinate=function(){return this._shell.getCoordinate()},e.prototype.getNumInteriorRing=function(){return this._holes.length},e.prototype.getBoundaryDimension=function(){return 1},e.prototype.getDimension=function(){return 2},e.prototype.getLength=function(){var t=this,e=0;e+=this._shell.getLength();for(var n=0;n<this._holes.length;n++)e+=t._holes[n].getLength();return e},e.prototype.getNumPoints=function(){for(var t=this,e=this._shell.getNumPoints(),n=0;n<this._holes.length;n++)e+=t._holes[n].getNumPoints();return e},e.prototype.reverse=function(){var t=this,e=this.copy();e._shell=this._shell.copy().reverse(),e._holes=new Array(this._holes.length).fill(null);for(var n=0;n<this._holes.length;n++)e._holes[n]=t._holes[n].copy().reverse();return e},e.prototype.convexHull=function(){return this.getExteriorRing().convexHull()},e.prototype.compareToSameClass=function(){var t=this;if(1===arguments.length){var e=arguments[0],n=this._shell,r=e._shell;return n.compareToSameClass(r)}if(2===arguments.length){var o=arguments[0],i=arguments[1],a=o,s=this._shell,c=a._shell,u=s.compareToSameClass(c,i);if(0!==u)return u;for(var l=this.getNumInteriorRing(),d=a.getNumInteriorRing(),p=0;p<l&&p<d;){var f=t.getInteriorRingN(p),h=a.getInteriorRingN(p),g=f.compareToSameClass(h,i);if(0!==g)return g;p++}return p<l?1:p<d?-1:0}},e.prototype.apply=function(t){var e=this;if(_f(t,nh)){this._shell.apply(t);for(var n=0;n<this._holes.length;n++)e._holes[n].apply(t)}else if(_f(t,Lh)){if(this._shell.apply(t),!t.isDone())for(var r=0;r<this._holes.length&&(e._holes[r].apply(t),!t.isDone());r++);t.isGeometryChanged()&&this.geometryChanged()}else if(_f(t,Mh))t.filter(this);else if(_f(t,Zf)){t.filter(this),this._shell.apply(t);for(var o=0;o<this._holes.length;o++)e._holes[o].apply(t)}},e.prototype.getBoundary=function(){var t=this;if(this.isEmpty())return this.getFactory().createMultiLineString();var e=new Array(this._holes.length+1).fill(null);e[0]=this._shell;for(var n=0;n<this._holes.length;n++)e[n+1]=t._holes[n]
;return e.length<=1?this.getFactory().createLinearRing(e[0].getCoordinateSequence()):this.getFactory().createMultiLineString(e)},e.prototype.clone=function(){var e=this,n=t.prototype.clone.call(this);n._shell=this._shell.clone(),n._holes=new Array(this._holes.length).fill(null);for(var r=0;r<this._holes.length;r++)n._holes[r]=e._holes[r].clone();return n},e.prototype.getGeometryType=function(){return"Polygon"},e.prototype.copy=function(){for(var t=this,n=this._shell.copy(),r=new Array(this._holes.length).fill(null),o=0;o<r.length;o++)r[o]=t._holes[o].copy();return new e(n,r,this._factory)},e.prototype.getExteriorRing=function(){return this._shell},e.prototype.isEmpty=function(){return this._shell.isEmpty()},e.prototype.getInteriorRingN=function(t){return this._holes[t]},e.prototype.interfaces_=function(){return[zh]},e.prototype.getClass=function(){return e},n.serialVersionUID.get=function(){return-0x307ffefd8dc97200},Object.defineProperties(e,n),e}(Qf),Yh=function(t){function e(){t.apply(this,arguments)}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={serialVersionUID:{configurable:!0}};return e.prototype.getSortIndex=function(){return Qf.SORTINDEX_MULTIPOINT},e.prototype.isValid=function(){return!0},e.prototype.equalsExact=function(){if(2===arguments.length){var e=arguments[0],n=arguments[1];return!!this.isEquivalentClass(e)&&t.prototype.equalsExact.call(this,e,n)}return t.prototype.equalsExact.apply(this,arguments)},e.prototype.getCoordinate=function(){if(1===arguments.length){var e=arguments[0];return this._geometries[e].getCoordinate()}return t.prototype.getCoordinate.apply(this,arguments)},e.prototype.getBoundaryDimension=function(){return Ah.FALSE},e.prototype.getDimension=function(){return 0},e.prototype.getBoundary=function(){return this.getFactory().createGeometryCollection(null)},e.prototype.getGeometryType=function(){return"MultiPoint"},e.prototype.copy=function(){for(var t=this,n=new Array(this._geometries.length).fill(null),r=0;r<n.length;r++)n[r]=t._geometries[r].copy();return new e(n,this._factory)},e.prototype.interfaces_=function(){return[Bh]},e.prototype.getClass=function(){return e},n.serialVersionUID.get=function(){return-0x6fb1ed4162e0fc00},Object.defineProperties(e,n),e}(Dh),Xh=function(t){function e(e,n){e instanceof ff&&n instanceof cg&&(e=n.getCoordinateSequenceFactory().create(e)),t.call(this,e,n),this.validateConstruction()}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={MINIMUM_VALID_SIZE:{configurable:!0},serialVersionUID:{configurable:!0}};return e.prototype.getSortIndex=function(){return Qf.SORTINDEX_LINEARRING},e.prototype.getBoundaryDimension=function(){return Ah.FALSE},e.prototype.isClosed=function(){return!!this.isEmpty()||t.prototype.isClosed.call(this)},e.prototype.reverse=function(){var t=this._points.copy();return kh.reverse(t),this.getFactory().createLinearRing(t)},e.prototype.validateConstruction=function(){if(!this.isEmpty()&&!t.prototype.isClosed.call(this))throw new sf("Points of LinearRing do not form a closed linestring");if(this.getCoordinateSequence().size()>=1&&this.getCoordinateSequence().size()<e.MINIMUM_VALID_SIZE)throw new sf("Invalid number of points in LinearRing (found "+this.getCoordinateSequence().size()+" - must be 0 or >= 4)")},e.prototype.getGeometryType=function(){return"LinearRing"},e.prototype.copy=function(){return new e(this._points.copy(),this._factory)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},n.MINIMUM_VALID_SIZE.get=function(){return 4},n.serialVersionUID.get=function(){return-0x3b229e262367a600},Object.defineProperties(e,n),e}(Uh),Wh=function(t){function e(){t.apply(this,arguments)}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={serialVersionUID:{configurable:!0}};return e.prototype.getSortIndex=function(){return Qf.SORTINDEX_MULTIPOLYGON},e.prototype.equalsExact=function(){if(2===arguments.length){var e=arguments[0],n=arguments[1];return!!this.isEquivalentClass(e)&&t.prototype.equalsExact.call(this,e,n)}return t.prototype.equalsExact.apply(this,arguments)},e.prototype.getBoundaryDimension=function(){return 1},e.prototype.getDimension=function(){return 2},e.prototype.reverse=function(){for(var t=this,e=this._geometries.length,n=new Array(e).fill(null),r=0;r<this._geometries.length;r++)n[r]=t._geometries[r].reverse();return this.getFactory().createMultiPolygon(n)},e.prototype.getBoundary=function(){var t=this;if(this.isEmpty())return this.getFactory().createMultiLineString();for(var e=new fh,n=0;n<this._geometries.length;n++)for(var r=t._geometries[n],o=r.getBoundary(),i=0;i<o.getNumGeometries();i++)e.add(o.getGeometryN(i));var a=new Array(e.size()).fill(null);return this.getFactory().createMultiLineString(e.toArray(a))},e.prototype.getGeometryType=function(){return"MultiPolygon"},e.prototype.copy=function(){for(var t=this,n=new Array(this._geometries.length).fill(null),r=0;r<n.length;r++)n[r]=t._geometries[r].copy();return new e(n,this._factory)},e.prototype.interfaces_=function(){return[zh]},e.prototype.getClass=function(){return e},n.serialVersionUID.get=function(){return-0x7a5aa1369171980},Object.defineProperties(e,n),e}(Dh),$h=function(t){this._factory=t||null,this._isUserDataCopied=!1},Hh={NoOpGeometryOperation:{configurable:!0},CoordinateOperation:{configurable:!0},CoordinateSequenceOperation:{configurable:!0}};$h.prototype.setCopyUserData=function(t){this._isUserDataCopied=t},$h.prototype.edit=function(t,e){if(null===t)return null;var n=this.editInternal(t,e);return this._isUserDataCopied&&n.setUserData(t.getUserData()),n},$h.prototype.editInternal=function(t,e){return null===this._factory&&(this._factory=t.getFactory()),t instanceof Dh?this.editGeometryCollection(t,e):t instanceof jh?this.editPolygon(t,e):t instanceof Vh?e.edit(t,this._factory):t instanceof Uh?e.edit(t,this._factory):(jf.shouldNeverReachHere("Unsupported Geometry class: "+t.getClass().getName()),null)},$h.prototype.editGeometryCollection=function(t,e){for(var n=this,r=e.edit(t,this._factory),o=new fh,i=0;i<r.getNumGeometries();i++){var a=n.edit(r.getGeometryN(i),e);null===a||a.isEmpty()||o.add(a)}return r.getClass()===Yh?this._factory.createMultiPoint(o.toArray([])):r.getClass()===Oh?this._factory.createMultiLineString(o.toArray([])):r.getClass()===Wh?this._factory.createMultiPolygon(o.toArray([])):this._factory.createGeometryCollection(o.toArray([]))},$h.prototype.editPolygon=function(t,e){var n=this,r=e.edit(t,this._factory);if(null===r&&(r=this._factory.createPolygon(null)),r.isEmpty())return r;var o=this.edit(r.getExteriorRing(),e);if(null===o||o.isEmpty())return this._factory.createPolygon();for(var i=new fh,a=0;a<r.getNumInteriorRing();a++){var s=n.edit(r.getInteriorRingN(a),e);null===s||s.isEmpty()||i.add(s)}return this._factory.createPolygon(o,i.toArray([]))},$h.prototype.interfaces_=function(){return[]},$h.prototype.getClass=function(){return $h},$h.GeometryEditorOperation=function(){},Hh.NoOpGeometryOperation.get=function(){return Kh},Hh.CoordinateOperation.get=function(){return Jh},Hh.CoordinateSequenceOperation.get=function(){return Zh},Object.defineProperties($h,Hh);var Kh=function(){};Kh.prototype.edit=function(t,e){return t},Kh.prototype.interfaces_=function(){return[$h.GeometryEditorOperation]},Kh.prototype.getClass=function(){return Kh};var Jh=function(){};Jh.prototype.edit=function(t,e){var n=this.editCoordinates(t.getCoordinates(),t);return null===n?t:t instanceof Xh?e.createLinearRing(n):t instanceof Uh?e.createLineString(n):t instanceof Vh?n.length>0?e.createPoint(n[0]):e.createPoint():t},Jh.prototype.interfaces_=function(){return[$h.GeometryEditorOperation]},Jh.prototype.getClass=function(){return Jh};var Zh=function(){};Zh.prototype.edit=function(t,e){return t instanceof Xh?e.createLinearRing(this.edit(t.getCoordinateSequence(),t)):t instanceof Uh?e.createLineString(this.edit(t.getCoordinateSequence(),t)):t instanceof Vh?e.createPoint(this.edit(t.getCoordinateSequence(),t)):t},Zh.prototype.interfaces_=function(){return[$h.GeometryEditorOperation]},Zh.prototype.getClass=function(){return Zh};var Qh=function(){var t=this;if(this._dimension=3,this._coordinates=null,1===arguments.length){if(arguments[0]instanceof Array)this._coordinates=arguments[0],this._dimension=3;else if(Number.isInteger(arguments[0])){var e=arguments[0];this._coordinates=new Array(e).fill(null);for(var n=0;n<e;n++)t._coordinates[n]=new ff}else if(_f(arguments[0],Nf)){var r=arguments[0];if(null===r)return this._coordinates=new Array(0).fill(null),null;this._dimension=r.getDimension(),this._coordinates=new Array(r.size()).fill(null);for(var o=0;o<this._coordinates.length;o++)t._coordinates[o]=r.getCoordinateCopy(o)}}else if(2===arguments.length)if(arguments[0]instanceof Array&&Number.isInteger(arguments[1])){var i=arguments[0],a=arguments[1];this._coordinates=i,this._dimension=a,null===i&&(this._coordinates=new Array(0).fill(null))}else if(Number.isInteger(arguments[0])&&Number.isInteger(arguments[1])){var s=arguments[0],c=arguments[1];this._coordinates=new Array(s).fill(null),this._dimension=c;for(var u=0;u<s;u++)t._coordinates[u]=new ff}},tg={serialVersionUID:{configurable:!0}};Qh.prototype.setOrdinate=function(t,e,n){switch(e){case Nf.X:this._coordinates[t].x=n;break;case Nf.Y:this._coordinates[t].y=n;break;case Nf.Z:this._coordinates[t].z=n;break;default:throw new sf("invalid ordinateIndex")}},Qh.prototype.size=function(){return this._coordinates.length},Qh.prototype.getOrdinate=function(t,e){switch(e){case Nf.X:return this._coordinates[t].x;case Nf.Y:return this._coordinates[t].y;case Nf.Z:return this._coordinates[t].z}return cf.NaN},Qh.prototype.getCoordinate=function(){if(1===arguments.length){var t=arguments[0];return this._coordinates[t]}if(2===arguments.length){var e=arguments[0],n=arguments[1];n.x=this._coordinates[e].x,n.y=this._coordinates[e].y,n.z=this._coordinates[e].z}},Qh.prototype.getCoordinateCopy=function(t){return new ff(this._coordinates[t])},Qh.prototype.getDimension=function(){return this._dimension},Qh.prototype.getX=function(t){return this._coordinates[t].x},Qh.prototype.clone=function(){for(var t=this,e=new Array(this.size()).fill(null),n=0;n<this._coordinates.length;n++)e[n]=t._coordinates[n].clone();return new Qh(e,this._dimension)},Qh.prototype.expandEnvelope=function(t){for(var e=this,n=0;n<this._coordinates.length;n++)t.expandToInclude(e._coordinates[n]);return t},Qh.prototype.copy=function(){for(var t=this,e=new Array(this.size()).fill(null),n=0;n<this._coordinates.length;n++)e[n]=t._coordinates[n].copy();return new Qh(e,this._dimension)},Qh.prototype.toString=function(){var t=this;if(this._coordinates.length>0){var e=new xf(17*this._coordinates.length);e.append("("),e.append(this._coordinates[0]);for(var n=1;n<this._coordinates.length;n++)e.append(", "),e.append(t._coordinates[n]);return e.append(")"),e.toString()}return"()"},Qh.prototype.getY=function(t){return this._coordinates[t].y},Qh.prototype.toCoordinateArray=function(){return this._coordinates},Qh.prototype.interfaces_=function(){return[Nf,ic]},Qh.prototype.getClass=function(){return Qh},tg.serialVersionUID.get=function(){return-0xcb44a778db18e00},Object.defineProperties(Qh,tg);var eg=function(){},ng={serialVersionUID:{configurable:!0},instanceObject:{configurable:!0}};eg.prototype.readResolve=function(){return eg.instance()},eg.prototype.create=function(){if(1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];return new Qh(t)}if(_f(arguments[0],Nf)){var e=arguments[0];return new Qh(e)}}else if(2===arguments.length){var n=arguments[0],r=arguments[1];return r>3&&(r=3),r<2?new Qh(n):new Qh(n,r)}},eg.prototype.interfaces_=function(){return[mf,ic]},eg.prototype.getClass=function(){return eg},eg.instance=function(){return eg.instanceObject},ng.serialVersionUID.get=function(){return-0x38e49fa6cf6f2e00},ng.instanceObject.get=function(){return new eg},Object.defineProperties(eg,ng);var rg=function(t){function e(){t.call(this),this.map_=new Map}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.get=function(t){return this.map_.get(t)||null},e.prototype.put=function(t,e){return this.map_.set(t,e),e},e.prototype.values=function(){for(var t=new fh,e=this.map_.values(),n=e.next();!n.done;)t.add(n.value),n=e.next();return t},e.prototype.entrySet=function(){var t=new xh;return this.map_.entries().forEach(function(e){return t.add(e)}),t},e.prototype.size=function(){return this.map_.size()},e}(bh),og=function t(){if(this._modelType=null,this._scale=null,0===arguments.length)this._modelType=t.FLOATING;else if(1===arguments.length)if(arguments[0]instanceof ag){var e=arguments[0];this._modelType=e,e===t.FIXED&&this.setScale(1)}else if("number"==typeof arguments[0]){var n=arguments[0];this._modelType=t.FIXED,this.setScale(n)}else if(arguments[0]instanceof t){var r=arguments[0];this._modelType=r._modelType,this._scale=r._scale}},ig={serialVersionUID:{configurable:!0},maximumPreciseValue:{configurable:!0}};og.prototype.equals=function(t){if(!(t instanceof og))return!1;var e=t;return this._modelType===e._modelType&&this._scale===e._scale},og.prototype.compareTo=function(t){var e=t,n=this.getMaximumSignificantDigits(),r=e.getMaximumSignificantDigits();return new Ef(n).compareTo(new Ef(r))},og.prototype.getScale=function(){return this._scale},og.prototype.isFloating=function(){return this._modelType===og.FLOATING||this._modelType===og.FLOATING_SINGLE},og.prototype.getType=function(){return this._modelType},og.prototype.toString=function(){var t="UNKNOWN";return this._modelType===og.FLOATING?t="Floating":this._modelType===og.FLOATING_SINGLE?t="Floating-Single":this._modelType===og.FIXED&&(t="Fixed (Scale="+this.getScale()+")"),t},og.prototype.makePrecise=function(){if("number"==typeof arguments[0]){var t=arguments[0];if(cf.isNaN(t))return t;if(this._modelType===og.FLOATING_SINGLE){return t}return this._modelType===og.FIXED?Math.round(t*this._scale)/this._scale:t}if(arguments[0]instanceof ff){var e=arguments[0];if(this._modelType===og.FLOATING)return null;e.x=this.makePrecise(e.x),e.y=this.makePrecise(e.y)}},og.prototype.getMaximumSignificantDigits=function(){var t=16;return this._modelType===og.FLOATING?t=16:this._modelType===og.FLOATING_SINGLE?t=6:this._modelType===og.FIXED&&(t=1+Math.trunc(Math.ceil(Math.log(this.getScale())/Math.log(10)))),t},og.prototype.setScale=function(t){this._scale=Math.abs(t)},og.prototype.interfaces_=function(){return[ic,lf]},og.prototype.getClass=function(){return og},og.mostPrecise=function(t,e){return t.compareTo(e)>=0?t:e},ig.serialVersionUID.get=function(){return 0x6bee6404e9a25c00},ig.maximumPreciseValue.get=function(){return 9007199254740992},Object.defineProperties(og,ig);var ag=function t(e){this._name=e||null,t.nameToTypeMap.put(e,this)},sg={serialVersionUID:{configurable:!0},nameToTypeMap:{configurable:!0}};ag.prototype.readResolve=function(){return ag.nameToTypeMap.get(this._name)},ag.prototype.toString=function(){return this._name},ag.prototype.interfaces_=function(){return[ic]},ag.prototype.getClass=function(){return ag},sg.serialVersionUID.get=function(){return-552860263173159e4},sg.nameToTypeMap.get=function(){return new rg},Object.defineProperties(ag,sg),og.Type=ag,og.FIXED=new ag("FIXED"),og.FLOATING=new ag("FLOATING"),og.FLOATING_SINGLE=new ag("FLOATING SINGLE");var cg=function t(){this._precisionModel=new og,this._SRID=0,this._coordinateSequenceFactory=t.getDefaultCoordinateSequenceFactory(),0===arguments.length||(1===arguments.length?_f(arguments[0],mf)?this._coordinateSequenceFactory=arguments[0]:arguments[0]instanceof og&&(this._precisionModel=arguments[0]):2===arguments.length?(this._precisionModel=arguments[0],this._SRID=arguments[1]):3===arguments.length&&(this._precisionModel=arguments[0],this._SRID=arguments[1],this._coordinateSequenceFactory=arguments[2]))},ug={serialVersionUID:{configurable:!0}};cg.prototype.toGeometry=function(t){return t.isNull()?this.createPoint(null):t.getMinX()===t.getMaxX()&&t.getMinY()===t.getMaxY()?this.createPoint(new ff(t.getMinX(),t.getMinY())):t.getMinX()===t.getMaxX()||t.getMinY()===t.getMaxY()?this.createLineString([new ff(t.getMinX(),t.getMinY()),new ff(t.getMaxX(),t.getMaxY())]):this.createPolygon(this.createLinearRing([new ff(t.getMinX(),t.getMinY()),new ff(t.getMinX(),t.getMaxY()),new ff(t.getMaxX(),t.getMaxY()),new ff(t.getMaxX(),t.getMinY()),new ff(t.getMinX(),t.getMinY())]),null)},cg.prototype.createLineString=function(t){return t?t instanceof Array?new Uh(this.getCoordinateSequenceFactory().create(t),this):_f(t,Nf)?new Uh(t,this):void 0:new Uh(this.getCoordinateSequenceFactory().create([]),this)},cg.prototype.createMultiLineString=function(){if(0===arguments.length)return new Oh(null,this);if(1===arguments.length){var t=arguments[0];return new Oh(t,this)}},cg.prototype.buildGeometry=function(t){for(var e=null,n=!1,r=!1,o=t.iterator();o.hasNext();){var i=o.next(),a=i.getClass();null===e&&(e=a),a!==e&&(n=!0),i.isGeometryCollectionOrDerived()&&(r=!0)}if(null===e)return this.createGeometryCollection();if(n||r)return this.createGeometryCollection(cg.toGeometryArray(t));var s=t.iterator().next();if(t.size()>1){if(s instanceof jh)return this.createMultiPolygon(cg.toPolygonArray(t));if(s instanceof Uh)return this.createMultiLineString(cg.toLineStringArray(t));if(s instanceof Vh)return this.createMultiPoint(cg.toPointArray(t));jf.shouldNeverReachHere("Unhandled class: "+s.getClass().getName())}return s},cg.prototype.createMultiPointFromCoords=function(t){return this.createMultiPoint(null!==t?this.getCoordinateSequenceFactory().create(t):null)},cg.prototype.createPoint=function(){if(0===arguments.length)return this.createPoint(this.getCoordinateSequenceFactory().create([]));if(1===arguments.length){if(arguments[0]instanceof ff){var t=arguments[0];return this.createPoint(null!==t?this.getCoordinateSequenceFactory().create([t]):null)}if(_f(arguments[0],Nf)){var e=arguments[0];return new Vh(e,this)}}},cg.prototype.getCoordinateSequenceFactory=function(){return this._coordinateSequenceFactory},cg.prototype.createPolygon=function(){if(0===arguments.length)return new jh(null,null,this);if(1===arguments.length){if(_f(arguments[0],Nf)){var t=arguments[0];return this.createPolygon(this.createLinearRing(t))}if(arguments[0]instanceof Array){var e=arguments[0];return this.createPolygon(this.createLinearRing(e))}if(arguments[0]instanceof Xh){var n=arguments[0];return this.createPolygon(n,null)}}else if(2===arguments.length){var r=arguments[0],o=arguments[1];return new jh(r,o,this)}},cg.prototype.getSRID=function(){return this._SRID},cg.prototype.createGeometryCollection=function(){if(0===arguments.length)return new Dh(null,this);if(1===arguments.length){var t=arguments[0];return new Dh(t,this)}},cg.prototype.createGeometry=function(t){return new $h(this).edit(t,{edit:function(){if(2===arguments.length){var t=arguments[0];return this._coordinateSequenceFactory.create(t)}}})},cg.prototype.getPrecisionModel=function(){return this._precisionModel},cg.prototype.createLinearRing=function(){if(0===arguments.length)return this.createLinearRing(this.getCoordinateSequenceFactory().create([]));if(1===arguments.length){if(arguments[0]instanceof Array){var t=arguments[0];return this.createLinearRing(null!==t?this.getCoordinateSequenceFactory().create(t):null)}if(_f(arguments[0],Nf)){var e=arguments[0];return new Xh(e,this)}}},cg.prototype.createMultiPolygon=function(){if(0===arguments.length)return new Wh(null,this);if(1===arguments.length){var t=arguments[0];return new Wh(t,this)}},cg.prototype.createMultiPoint=function(){var t=this;if(0===arguments.length)return new Yh(null,this);if(1===arguments.length){if(arguments[0]instanceof Array){var e=arguments[0];return new Yh(e,this)}if(arguments[0]instanceof Array){var n=arguments[0];return this.createMultiPoint(null!==n?this.getCoordinateSequenceFactory().create(n):null)}if(_f(arguments[0],Nf)){var r=arguments[0];if(null===r)return this.createMultiPoint(new Array(0).fill(null));for(var o=new Array(r.size()).fill(null),i=0;i<r.size();i++){var a=t.getCoordinateSequenceFactory().create(1,r.getDimension());kh.copy(r,i,a,0,1),o[i]=t.createPoint(a)}return this.createMultiPoint(o)}}},cg.prototype.interfaces_=function(){return[ic]},cg.prototype.getClass=function(){return cg},cg.toMultiPolygonArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},cg.toGeometryArray=function(t){if(null===t)return null;var e=new Array(t.size()).fill(null);return t.toArray(e)},cg.getDefaultCoordinateSequenceFactory=function(){return eg.instance()},cg.toMultiLineStringArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},cg.toLineStringArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},cg.toMultiPointArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},cg.toLinearRingArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},cg.toPointArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},cg.toPolygonArray=function(t){var e=new Array(t.size()).fill(null);return t.toArray(e)},cg.createPointFromInternalCoord=function(t,e){return e.getPrecisionModel().makePrecise(t),e.getFactory().createPoint(t)},ug.serialVersionUID.get=function(){return-0x5ea75f2051eeb400},Object.defineProperties(cg,ug);var lg=["Point","MultiPoint","LineString","MultiLineString","Polygon","MultiPolygon"],dg=function(t){this.geometryFactory=t||new cg};dg.prototype.read=function(t){var e;e="string"==typeof t?JSON.parse(t):t;var n=e.type;if(!pg[n])throw new Error("Unknown GeoJSON type: "+e.type);return-1!==lg.indexOf(n)?pg[n].apply(this,[e.coordinates]):"GeometryCollection"===n?pg[n].apply(this,[e.geometries]):pg[n].apply(this,[e])},dg.prototype.write=function(t){var e=t.getGeometryType();if(!fg[e])throw new Error("Geometry is not supported");return fg[e].apply(this,[t])};var pg={Feature:function(t){var e={};for(var n in t)e[n]=t[n];if(t.geometry){var r=t.geometry.type;if(!pg[r])throw new Error("Unknown GeoJSON type: "+t.type);e.geometry=this.read(t.geometry)}return t.bbox&&(e.bbox=pg.bbox.apply(this,[t.bbox])),e},FeatureCollection:function(t){var e=this,n={};if(t.features){n.features=[];for(var r=0;r<t.features.length;++r)n.features.push(e.read(t.features[r]))}return t.bbox&&(n.bbox=this.parse.bbox.apply(this,[t.bbox])),n},coordinates:function(t){for(var e=[],n=0;n<t.length;++n){var r=t[n];e.push(new ff(r[0],r[1]))}return e},bbox:function(t){return this.geometryFactory.createLinearRing([new ff(t[0],t[1]),new ff(t[2],t[1]),new ff(t[2],t[3]),new ff(t[0],t[3]),new ff(t[0],t[1])])},Point:function(t){var e=new ff(t[0],t[1]);return this.geometryFactory.createPoint(e)},MultiPoint:function(t){for(var e=this,n=[],r=0;r<t.length;++r)n.push(pg.Point.apply(e,[t[r]]));return this.geometryFactory.createMultiPoint(n)},LineString:function(t){var e=pg.coordinates.apply(this,[t]);return this.geometryFactory.createLineString(e)},MultiLineString:function(t){for(var e=this,n=[],r=0;r<t.length;++r)n.push(pg.LineString.apply(e,[t[r]]));return this.geometryFactory.createMultiLineString(n)},Polygon:function(t){for(var e=this,n=pg.coordinates.apply(this,[t[0]]),r=this.geometryFactory.createLinearRing(n),o=[],i=1;i<t.length;++i){var a=t[i],s=pg.coordinates.apply(e,[a]),c=e.geometryFactory.createLinearRing(s);o.push(c)}return this.geometryFactory.createPolygon(r,o)},MultiPolygon:function(t){for(var e=this,n=[],r=0;r<t.length;++r){var o=t[r];n.push(pg.Polygon.apply(e,[o]))}return this.geometryFactory.createMultiPolygon(n)},GeometryCollection:function(t){for(var e=this,n=[],r=0;r<t.length;++r){var o=t[r];n.push(e.read(o))}return this.geometryFactory.createGeometryCollection(n)}},fg={coordinate:function(t){return[t.x,t.y]},Point:function(t){return{type:"Point",coordinates:fg.coordinate.apply(this,[t.getCoordinate()])}},MultiPoint:function(t){for(var e=this,n=[],r=0;r<t._geometries.length;++r){var o=t._geometries[r],i=fg.Point.apply(e,[o]);n.push(i.coordinates)}return{type:"MultiPoint",coordinates:n}},LineString:function(t){for(var e=this,n=[],r=t.getCoordinates(),o=0;o<r.length;++o){var i=r[o];n.push(fg.coordinate.apply(e,[i]))}return{type:"LineString",coordinates:n}},MultiLineString:function(t){for(var e=this,n=[],r=0;r<t._geometries.length;++r){var o=t._geometries[r],i=fg.LineString.apply(e,[o]);n.push(i.coordinates)}return{type:"MultiLineString",coordinates:n}},Polygon:function(t){var e=this,n=[],r=fg.LineString.apply(this,[t._shell]);n.push(r.coordinates);for(var o=0;o<t._holes.length;++o){var i=t._holes[o],a=fg.LineString.apply(e,[i]);n.push(a.coordinates)}return{type:"Polygon",coordinates:n}},MultiPolygon:function(t){for(var e=this,n=[],r=0;r<t._geometries.length;++r){var o=t._geometries[r],i=fg.Polygon.apply(e,[o]);n.push(i.coordinates)}return{type:"MultiPolygon",coordinates:n}},GeometryCollection:function(t){for(var e=this,n=[],r=0;r<t._geometries.length;++r){var o=t._geometries[r],i=o.getGeometryType();n.push(fg[i].apply(e,[o]))}return{type:"GeometryCollection",geometries:n}}},hg=function(t){this.geometryFactory=t||new cg,this.precisionModel=this.geometryFactory.getPrecisionModel(),this.parser=new dg(this.geometryFactory)};hg.prototype.read=function(t){var e=this.parser.read(t);return this.precisionModel.getType()===og.FIXED&&this.reducePrecision(e),e},hg.prototype.reducePrecision=function(t){var e,n,r=this;if(t.coordinate)this.precisionModel.makePrecise(t.coordinate);else if(t.points)for(e=0,n=t.points.length;e<n;e++)r.precisionModel.makePrecise(t.points[e]);else if(t.geometries)for(e=0,n=t.geometries.length;e<n;e++)r.reducePrecision(t.geometries[e])};var gg=function(){this.parser=new dg(this.geometryFactory)};gg.prototype.write=function(t){return this.parser.write(t)};var mg=function(){},yg={ON:{configurable:!0},LEFT:{configurable:!0},RIGHT:{configurable:!0}};mg.prototype.interfaces_=function(){return[]},mg.prototype.getClass=function(){return mg},mg.opposite=function(t){return t===mg.LEFT?mg.RIGHT:t===mg.RIGHT?mg.LEFT:t},yg.ON.get=function(){return 0},yg.LEFT.get=function(){return 1},yg.RIGHT.get=function(){return 2},Object.defineProperties(mg,yg),xc.prototype=new Error,xc.prototype.name="EmptyStackException",Ec.prototype=new ph,Ec.prototype.add=function(t){return this.array_.push(t),!0},Ec.prototype.get=function(t){if(t<0||t>=this.size())throw new Error;return this.array_[t]},Ec.prototype.push=function(t){return this.array_.push(t),t},Ec.prototype.pop=function(t){if(0===this.array_.length)throw new xc;return this.array_.pop()},Ec.prototype.peek=function(){if(0===this.array_.length)throw new xc;return this.array_[this.array_.length-1]},Ec.prototype.empty=function(){return 0===this.array_.length},Ec.prototype.isEmpty=function(){return this.empty()},Ec.prototype.search=function(t){return this.array_.indexOf(t)},Ec.prototype.size=function(){return this.array_.length},Ec.prototype.toArray=function(){for(var t=this,e=[],n=0,r=this.array_.length;n<r;n++)e.push(t.array_[n]);return e};var vg=function(){this._minIndex=-1,this._minCoord=null,this._minDe=null,this._orientedDe=null};vg.prototype.getCoordinate=function(){return this._minCoord},vg.prototype.getRightmostSide=function(t,e){var n=this.getRightmostSideOfSegment(t,e);return n<0&&(n=this.getRightmostSideOfSegment(t,e-1)),n<0&&(this._minCoord=null,this.checkForRightmostCoordinate(t)),n},vg.prototype.findRightmostEdgeAtVertex=function(){var t=this._minDe.getEdge().getCoordinates();jf.isTrue(this._minIndex>0&&this._minIndex<t.length,"rightmost point expected to be interior vertex of edge");var e=t[this._minIndex-1],n=t[this._minIndex+1],r=Kf.computeOrientation(this._minCoord,n,e),o=!1;e.y<this._minCoord.y&&n.y<this._minCoord.y&&r===Kf.COUNTERCLOCKWISE?o=!0:e.y>this._minCoord.y&&n.y>this._minCoord.y&&r===Kf.CLOCKWISE&&(o=!0),o&&(this._minIndex=this._minIndex-1)},vg.prototype.getRightmostSideOfSegment=function(t,e){var n=t.getEdge(),r=n.getCoordinates();if(e<0||e+1>=r.length)return-1;if(r[e].y===r[e+1].y)return-1;var o=mg.LEFT;return r[e].y<r[e+1].y&&(o=mg.RIGHT),o},vg.prototype.getEdge=function(){return this._orientedDe},vg.prototype.checkForRightmostCoordinate=function(t){for(var e=this,n=t.getEdge().getCoordinates(),r=0;r<n.length-1;r++)(null===e._minCoord||n[r].x>e._minCoord.x)&&(e._minDe=t,e._minIndex=r,e._minCoord=n[r])},vg.prototype.findRightmostEdgeAtNode=function(){var t=this._minDe.getNode(),e=t.getEdges();this._minDe=e.getRightmostEdge(),this._minDe.isForward()||(this._minDe=this._minDe.getSym(),this._minIndex=this._minDe.getEdge().getCoordinates().length-1)},vg.prototype.findEdge=function(t){for(var e=this,n=t.iterator();n.hasNext();){var r=n.next();r.isForward()&&e.checkForRightmostCoordinate(r)}jf.isTrue(0!==this._minIndex||this._minCoord.equals(this._minDe.getCoordinate()),"inconsistency in rightmost processing"),0===this._minIndex?this.findRightmostEdgeAtNode():this.findRightmostEdgeAtVertex(),this._orientedDe=this._minDe,this.getRightmostSide(this._minDe,this._minIndex)===mg.LEFT&&(this._orientedDe=this._minDe.getSym())},vg.prototype.interfaces_=function(){return[]},vg.prototype.getClass=function(){return vg};var _g=function(t){function e(n,r){t.call(this,e.msgWithCoord(n,r)),this.pt=r?new ff(r):null,this.name="TopologyException"}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.getCoordinate=function(){return this.pt},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e.msgWithCoord=function(t,e){return e?t:t+" [ "+e+" ]"},e}(Vf),bg=function(){this.array_=[]};bg.prototype.addLast=function(t){this.array_.push(t)},bg.prototype.removeFirst=function(){return this.array_.shift()},bg.prototype.isEmpty=function(){return 0===this.array_.length};var wg=function(){this._finder=null,this._dirEdgeList=new fh,this._nodes=new fh,this._rightMostCoord=null,this._env=null,this._finder=new vg};wg.prototype.clearVisitedEdges=function(){for(var t=this._dirEdgeList.iterator();t.hasNext();){t.next().setVisited(!1)}},wg.prototype.getRightmostCoordinate=function(){return this._rightMostCoord},wg.prototype.computeNodeDepth=function(t){for(var e=this,n=null,r=t.getEdges().iterator();r.hasNext();){var o=r.next();if(o.isVisited()||o.getSym().isVisited()){n=o;break}}if(null===n)throw new _g("unable to find edge to compute depths at "+t.getCoordinate());t.getEdges().computeDepths(n);for(var i=t.getEdges().iterator();i.hasNext();){var a=i.next();a.setVisited(!0),e.copySymDepths(a)}},wg.prototype.computeDepth=function(t){this.clearVisitedEdges();var e=this._finder.getEdge();e.setEdgeDepths(mg.RIGHT,t),this.copySymDepths(e),this.computeDepths(e)},wg.prototype.create=function(t){this.addReachable(t),this._finder.findEdge(this._dirEdgeList),this._rightMostCoord=this._finder.getCoordinate()},wg.prototype.findResultEdges=function(){for(var t=this._dirEdgeList.iterator();t.hasNext();){var e=t.next();e.getDepth(mg.RIGHT)>=1&&e.getDepth(mg.LEFT)<=0&&!e.isInteriorAreaEdge()&&e.setInResult(!0)}},wg.prototype.computeDepths=function(t){var e=this,n=new xh,r=new bg,o=t.getNode();for(r.addLast(o),n.add(o),t.setVisited(!0);!r.isEmpty();){var i=r.removeFirst();n.add(i),e.computeNodeDepth(i);for(var a=i.getEdges().iterator();a.hasNext();){var s=a.next(),c=s.getSym();if(!c.isVisited()){var u=c.getNode();n.contains(u)||(r.addLast(u),n.add(u))}}}},wg.prototype.compareTo=function(t){var e=t;return this._rightMostCoord.x<e._rightMostCoord.x?-1:this._rightMostCoord.x>e._rightMostCoord.x?1:0},wg.prototype.getEnvelope=function(){if(null===this._env){for(var t=new Rf,e=this._dirEdgeList.iterator();e.hasNext();)for(var n=e.next(),r=n.getEdge().getCoordinates(),o=0;o<r.length-1;o++)t.expandToInclude(r[o]);this._env=t}return this._env},wg.prototype.addReachable=function(t){var e=this,n=new Ec;for(n.add(t);!n.empty();){var r=n.pop();e.add(r,n)}},wg.prototype.copySymDepths=function(t){
var e=t.getSym();e.setDepth(mg.LEFT,t.getDepth(mg.RIGHT)),e.setDepth(mg.RIGHT,t.getDepth(mg.LEFT))},wg.prototype.add=function(t,e){var n=this;t.setVisited(!0),this._nodes.add(t);for(var r=t.getEdges().iterator();r.hasNext();){var o=r.next();n._dirEdgeList.add(o);var i=o.getSym(),a=i.getNode();a.isVisited()||e.push(a)}},wg.prototype.getNodes=function(){return this._nodes},wg.prototype.getDirectedEdges=function(){return this._dirEdgeList},wg.prototype.interfaces_=function(){return[lf]},wg.prototype.getClass=function(){return wg};var xg=function t(){var e=this;if(this.location=null,1===arguments.length){if(arguments[0]instanceof Array){var n=arguments[0];this.init(n.length)}else if(Number.isInteger(arguments[0])){var r=arguments[0];this.init(1),this.location[mg.ON]=r}else if(arguments[0]instanceof t){var o=arguments[0];if(this.init(o.location.length),null!==o)for(var i=0;i<this.location.length;i++)e.location[i]=o.location[i]}}else if(3===arguments.length){var a=arguments[0],s=arguments[1],c=arguments[2];this.init(3),this.location[mg.ON]=a,this.location[mg.LEFT]=s,this.location[mg.RIGHT]=c}};xg.prototype.setAllLocations=function(t){for(var e=this,n=0;n<this.location.length;n++)e.location[n]=t},xg.prototype.isNull=function(){for(var t=this,e=0;e<this.location.length;e++)if(t.location[e]!==yf.NONE)return!1;return!0},xg.prototype.setAllLocationsIfNull=function(t){for(var e=this,n=0;n<this.location.length;n++)e.location[n]===yf.NONE&&(e.location[n]=t)},xg.prototype.isLine=function(){return 1===this.location.length},xg.prototype.merge=function(t){var e=this;if(t.location.length>this.location.length){var n=new Array(3).fill(null);n[mg.ON]=this.location[mg.ON],n[mg.LEFT]=yf.NONE,n[mg.RIGHT]=yf.NONE,this.location=n}for(var r=0;r<this.location.length;r++)e.location[r]===yf.NONE&&r<t.location.length&&(e.location[r]=t.location[r])},xg.prototype.getLocations=function(){return this.location},xg.prototype.flip=function(){if(this.location.length<=1)return null;var t=this.location[mg.LEFT];this.location[mg.LEFT]=this.location[mg.RIGHT],this.location[mg.RIGHT]=t},xg.prototype.toString=function(){var t=new xf;return this.location.length>1&&t.append(yf.toLocationSymbol(this.location[mg.LEFT])),t.append(yf.toLocationSymbol(this.location[mg.ON])),this.location.length>1&&t.append(yf.toLocationSymbol(this.location[mg.RIGHT])),t.toString()},xg.prototype.setLocations=function(t,e,n){this.location[mg.ON]=t,this.location[mg.LEFT]=e,this.location[mg.RIGHT]=n},xg.prototype.get=function(t){return t<this.location.length?this.location[t]:yf.NONE},xg.prototype.isArea=function(){return this.location.length>1},xg.prototype.isAnyNull=function(){for(var t=this,e=0;e<this.location.length;e++)if(t.location[e]===yf.NONE)return!0;return!1},xg.prototype.setLocation=function(){if(1===arguments.length){var t=arguments[0];this.setLocation(mg.ON,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.location[e]=n}},xg.prototype.init=function(t){this.location=new Array(t).fill(null),this.setAllLocations(yf.NONE)},xg.prototype.isEqualOnSide=function(t,e){return this.location[e]===t.location[e]},xg.prototype.allPositionsEqual=function(t){for(var e=this,n=0;n<this.location.length;n++)if(e.location[n]!==t)return!1;return!0},xg.prototype.interfaces_=function(){return[]},xg.prototype.getClass=function(){return xg};var Eg=function t(){if(this.elt=new Array(2).fill(null),1===arguments.length){if(Number.isInteger(arguments[0])){var e=arguments[0];this.elt[0]=new xg(e),this.elt[1]=new xg(e)}else if(arguments[0]instanceof t){var n=arguments[0];this.elt[0]=new xg(n.elt[0]),this.elt[1]=new xg(n.elt[1])}}else if(2===arguments.length){var r=arguments[0],o=arguments[1];this.elt[0]=new xg(yf.NONE),this.elt[1]=new xg(yf.NONE),this.elt[r].setLocation(o)}else if(3===arguments.length){var i=arguments[0],a=arguments[1],s=arguments[2];this.elt[0]=new xg(i,a,s),this.elt[1]=new xg(i,a,s)}else if(4===arguments.length){var c=arguments[0],u=arguments[1],l=arguments[2],d=arguments[3];this.elt[0]=new xg(yf.NONE,yf.NONE,yf.NONE),this.elt[1]=new xg(yf.NONE,yf.NONE,yf.NONE),this.elt[c].setLocations(u,l,d)}};Eg.prototype.getGeometryCount=function(){var t=0;return this.elt[0].isNull()||t++,this.elt[1].isNull()||t++,t},Eg.prototype.setAllLocations=function(t,e){this.elt[t].setAllLocations(e)},Eg.prototype.isNull=function(t){return this.elt[t].isNull()},Eg.prototype.setAllLocationsIfNull=function(){if(1===arguments.length){var t=arguments[0];this.setAllLocationsIfNull(0,t),this.setAllLocationsIfNull(1,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.elt[e].setAllLocationsIfNull(n)}},Eg.prototype.isLine=function(t){return this.elt[t].isLine()},Eg.prototype.merge=function(t){for(var e=this,n=0;n<2;n++)null===e.elt[n]&&null!==t.elt[n]?e.elt[n]=new xg(t.elt[n]):e.elt[n].merge(t.elt[n])},Eg.prototype.flip=function(){this.elt[0].flip(),this.elt[1].flip()},Eg.prototype.getLocation=function(){if(1===arguments.length){var t=arguments[0];return this.elt[t].get(mg.ON)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return this.elt[e].get(n)}},Eg.prototype.toString=function(){var t=new xf;return null!==this.elt[0]&&(t.append("A:"),t.append(this.elt[0].toString())),null!==this.elt[1]&&(t.append(" B:"),t.append(this.elt[1].toString())),t.toString()},Eg.prototype.isArea=function(){if(0===arguments.length)return this.elt[0].isArea()||this.elt[1].isArea();if(1===arguments.length){var t=arguments[0];return this.elt[t].isArea()}},Eg.prototype.isAnyNull=function(t){return this.elt[t].isAnyNull()},Eg.prototype.setLocation=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];this.elt[t].setLocation(mg.ON,e)}else if(3===arguments.length){var n=arguments[0],r=arguments[1],o=arguments[2];this.elt[n].setLocation(r,o)}},Eg.prototype.isEqualOnSide=function(t,e){return this.elt[0].isEqualOnSide(t.elt[0],e)&&this.elt[1].isEqualOnSide(t.elt[1],e)},Eg.prototype.allPositionsEqual=function(t,e){return this.elt[t].allPositionsEqual(e)},Eg.prototype.toLine=function(t){this.elt[t].isArea()&&(this.elt[t]=new xg(this.elt[t].location[0]))},Eg.prototype.interfaces_=function(){return[]},Eg.prototype.getClass=function(){return Eg},Eg.toLineLabel=function(t){for(var e=new Eg(yf.NONE),n=0;n<2;n++)e.setLocation(n,t.getLocation(n));return e};var Cg=function(){this._startDe=null,this._maxNodeDegree=-1,this._edges=new fh,this._pts=new fh,this._label=new Eg(yf.NONE),this._ring=null,this._isHole=null,this._shell=null,this._holes=new fh,this._geometryFactory=null;var t=arguments[0],e=arguments[1];this._geometryFactory=e,this.computePoints(t),this.computeRing()};Cg.prototype.computeRing=function(){var t=this;if(null!==this._ring)return null;for(var e=new Array(this._pts.size()).fill(null),n=0;n<this._pts.size();n++)e[n]=t._pts.get(n);this._ring=this._geometryFactory.createLinearRing(e),this._isHole=Kf.isCCW(this._ring.getCoordinates())},Cg.prototype.isIsolated=function(){return 1===this._label.getGeometryCount()},Cg.prototype.computePoints=function(t){var e=this;this._startDe=t;var n=t,r=!0;do{if(null===n)throw new _g("Found null DirectedEdge");if(n.getEdgeRing()===e)throw new _g("Directed Edge visited twice during ring-building at "+n.getCoordinate());e._edges.add(n);var o=n.getLabel();jf.isTrue(o.isArea()),e.mergeLabel(o),e.addPoints(n.getEdge(),n.isForward(),r),r=!1,e.setEdgeRing(n,e),n=e.getNext(n)}while(n!==this._startDe)},Cg.prototype.getLinearRing=function(){return this._ring},Cg.prototype.getCoordinate=function(t){return this._pts.get(t)},Cg.prototype.computeMaxNodeDegree=function(){var t=this;this._maxNodeDegree=0;var e=this._startDe;do{var n=e.getNode(),r=n.getEdges().getOutgoingDegree(t);r>t._maxNodeDegree&&(t._maxNodeDegree=r),e=t.getNext(e)}while(e!==this._startDe);this._maxNodeDegree*=2},Cg.prototype.addPoints=function(t,e,n){var r=this,o=t.getCoordinates();if(e){var i=1;n&&(i=0);for(var a=i;a<o.length;a++)r._pts.add(o[a])}else{var s=o.length-2;n&&(s=o.length-1);for(var c=s;c>=0;c--)r._pts.add(o[c])}},Cg.prototype.isHole=function(){return this._isHole},Cg.prototype.setInResult=function(){var t=this._startDe;do{t.getEdge().setInResult(!0),t=t.getNext()}while(t!==this._startDe)},Cg.prototype.containsPoint=function(t){var e=this.getLinearRing();if(!e.getEnvelopeInternal().contains(t))return!1;if(!Kf.isPointInRing(t,e.getCoordinates()))return!1;for(var n=this._holes.iterator();n.hasNext();){if(n.next().containsPoint(t))return!1}return!0},Cg.prototype.addHole=function(t){this._holes.add(t)},Cg.prototype.isShell=function(){return null===this._shell},Cg.prototype.getLabel=function(){return this._label},Cg.prototype.getEdges=function(){return this._edges},Cg.prototype.getMaxNodeDegree=function(){return this._maxNodeDegree<0&&this.computeMaxNodeDegree(),this._maxNodeDegree},Cg.prototype.getShell=function(){return this._shell},Cg.prototype.mergeLabel=function(){if(1===arguments.length){var t=arguments[0];this.mergeLabel(t,0),this.mergeLabel(t,1)}else if(2===arguments.length){var e=arguments[0],n=arguments[1],r=e.getLocation(n,mg.RIGHT);if(r===yf.NONE)return null;if(this._label.getLocation(n)===yf.NONE)return this._label.setLocation(n,r),null}},Cg.prototype.setShell=function(t){this._shell=t,null!==t&&t.addHole(this)},Cg.prototype.toPolygon=function(t){for(var e=this,n=new Array(this._holes.size()).fill(null),r=0;r<this._holes.size();r++)n[r]=e._holes.get(r).getLinearRing();return t.createPolygon(this.getLinearRing(),n)},Cg.prototype.interfaces_=function(){return[]},Cg.prototype.getClass=function(){return Cg};var Sg=function(t){function e(){var e=arguments[0],n=arguments[1];t.call(this,e,n)}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.setEdgeRing=function(t,e){t.setMinEdgeRing(e)},e.prototype.getNext=function(t){return t.getNextMin()},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(Cg),Ig=function(t){function e(){var e=arguments[0],n=arguments[1];t.call(this,e,n)}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.buildMinimalRings=function(){var t=this,e=new fh,n=this._startDe;do{if(null===n.getMinEdgeRing()){var r=new Sg(n,t._geometryFactory);e.add(r)}n=n.getNext()}while(n!==this._startDe);return e},e.prototype.setEdgeRing=function(t,e){t.setEdgeRing(e)},e.prototype.linkDirectedEdgesForMinimalEdgeRings=function(){var t=this,e=this._startDe;do{e.getNode().getEdges().linkMinimalDirectedEdges(t),e=e.getNext()}while(e!==this._startDe)},e.prototype.getNext=function(t){return t.getNext()},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(Cg),Pg=function(){if(this._label=null,this._isInResult=!1,this._isCovered=!1,this._isCoveredSet=!1,this._isVisited=!1,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this._label=t}};Pg.prototype.setVisited=function(t){this._isVisited=t},Pg.prototype.setInResult=function(t){this._isInResult=t},Pg.prototype.isCovered=function(){return this._isCovered},Pg.prototype.isCoveredSet=function(){return this._isCoveredSet},Pg.prototype.setLabel=function(t){this._label=t},Pg.prototype.getLabel=function(){return this._label},Pg.prototype.setCovered=function(t){this._isCovered=t,this._isCoveredSet=!0},Pg.prototype.updateIM=function(t){jf.isTrue(this._label.getGeometryCount()>=2,"found partial label"),this.computeIM(t)},Pg.prototype.isInResult=function(){return this._isInResult},Pg.prototype.isVisited=function(){return this._isVisited},Pg.prototype.interfaces_=function(){return[]},Pg.prototype.getClass=function(){return Pg};var Ag=function(t){function e(){t.call(this),this._coord=null,this._edges=null;var e=arguments[0],n=arguments[1];this._coord=e,this._edges=n,this._label=new Eg(0,yf.NONE)}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.isIncidentEdgeInResult=function(){for(var t=this.getEdges().getEdges().iterator();t.hasNext();){if(t.next().getEdge().isInResult())return!0}return!1},e.prototype.isIsolated=function(){return 1===this._label.getGeometryCount()},e.prototype.getCoordinate=function(){return this._coord},e.prototype.print=function(t){t.println("node "+this._coord+" lbl: "+this._label)},e.prototype.computeIM=function(t){},e.prototype.computeMergedLocation=function(t,e){var n=yf.NONE;if(n=this._label.getLocation(e),!t.isNull(e)){var r=t.getLocation(e);n!==yf.BOUNDARY&&(n=r)}return n},e.prototype.setLabel=function(){if(2!==arguments.length)return t.prototype.setLabel.apply(this,arguments);var e=arguments[0],n=arguments[1];null===this._label?this._label=new Eg(e,n):this._label.setLocation(e,n)},e.prototype.getEdges=function(){return this._edges},e.prototype.mergeLabel=function(){var t=this;if(arguments[0]instanceof e){var n=arguments[0];this.mergeLabel(n._label)}else if(arguments[0]instanceof Eg)for(var r=arguments[0],o=0;o<2;o++){var i=t.computeMergedLocation(r,o),a=t._label.getLocation(o);a===yf.NONE&&t._label.setLocation(o,i)}},e.prototype.add=function(t){this._edges.insert(t),t.setNode(this)},e.prototype.setLabelBoundary=function(t){if(null===this._label)return null;var e=yf.NONE;null!==this._label&&(e=this._label.getLocation(t));var n=null;switch(e){case yf.BOUNDARY:n=yf.INTERIOR;break;case yf.INTERIOR:default:n=yf.BOUNDARY}this._label.setLocation(t,n)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(Pg),Ng=function(){this.nodeMap=new hc,this.nodeFact=null;var t=arguments[0];this.nodeFact=t};Ng.prototype.find=function(t){return this.nodeMap.get(t)},Ng.prototype.addNode=function(){if(arguments[0]instanceof ff){var t=arguments[0],e=this.nodeMap.get(t);return null===e&&(e=this.nodeFact.createNode(t),this.nodeMap.put(t,e)),e}if(arguments[0]instanceof Ag){var n=arguments[0],r=this.nodeMap.get(n.getCoordinate());return null===r?(this.nodeMap.put(n.getCoordinate(),n),n):(r.mergeLabel(n),r)}},Ng.prototype.print=function(t){for(var e=this.iterator();e.hasNext();){e.next().print(t)}},Ng.prototype.iterator=function(){return this.nodeMap.values().iterator()},Ng.prototype.values=function(){return this.nodeMap.values()},Ng.prototype.getBoundaryNodes=function(t){for(var e=new fh,n=this.iterator();n.hasNext();){var r=n.next();r.getLabel().getLocation(t)===yf.BOUNDARY&&e.add(r)}return e},Ng.prototype.add=function(t){var e=t.getCoordinate();this.addNode(e).add(t)},Ng.prototype.interfaces_=function(){return[]},Ng.prototype.getClass=function(){return Ng};var Mg=function(){},Lg={NE:{configurable:!0},NW:{configurable:!0},SW:{configurable:!0},SE:{configurable:!0}};Mg.prototype.interfaces_=function(){return[]},Mg.prototype.getClass=function(){return Mg},Mg.isNorthern=function(t){return t===Mg.NE||t===Mg.NW},Mg.isOpposite=function(t,e){return t!==e&&2==(t-e+4)%4},Mg.commonHalfPlane=function(t,e){if(t===e)return t;if(2==(t-e+4)%4)return-1;var n=t<e?t:e,r=t>e?t:e;return 0===n&&3===r?3:n},Mg.isInHalfPlane=function(t,e){return e===Mg.SE?t===Mg.SE||t===Mg.SW:t===e||t===e+1},Mg.quadrant=function(){if("number"==typeof arguments[0]&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1];if(0===t&&0===e)throw new sf("Cannot compute the quadrant for point ( "+t+", "+e+" )");return t>=0?e>=0?Mg.NE:Mg.SE:e>=0?Mg.NW:Mg.SW}if(arguments[0]instanceof ff&&arguments[1]instanceof ff){var n=arguments[0],r=arguments[1];if(r.x===n.x&&r.y===n.y)throw new sf("Cannot compute the quadrant for two identical points "+n);return r.x>=n.x?r.y>=n.y?Mg.NE:Mg.SE:r.y>=n.y?Mg.NW:Mg.SW}},Lg.NE.get=function(){return 0},Lg.NW.get=function(){return 1},Lg.SW.get=function(){return 2},Lg.SE.get=function(){return 3},Object.defineProperties(Mg,Lg);var Dg=function(){if(this._edge=null,this._label=null,this._node=null,this._p0=null,this._p1=null,this._dx=null,this._dy=null,this._quadrant=null,1===arguments.length){var t=arguments[0];this._edge=t}else if(3===arguments.length){var e=arguments[0],n=arguments[1],r=arguments[2];this._edge=e,this.init(n,r),this._label=null}else if(4===arguments.length){var o=arguments[0],i=arguments[1],a=arguments[2],s=arguments[3];this._edge=o,this.init(i,a),this._label=s}};Dg.prototype.compareDirection=function(t){return this._dx===t._dx&&this._dy===t._dy?0:this._quadrant>t._quadrant?1:this._quadrant<t._quadrant?-1:Kf.computeOrientation(t._p0,t._p1,this._p1)},Dg.prototype.getDy=function(){return this._dy},Dg.prototype.getCoordinate=function(){return this._p0},Dg.prototype.setNode=function(t){this._node=t},Dg.prototype.print=function(t){var e=Math.atan2(this._dy,this._dx),n=this.getClass().getName(),r=n.lastIndexOf("."),o=n.substring(r+1);t.print("  "+o+": "+this._p0+" - "+this._p1+" "+this._quadrant+":"+e+"   "+this._label)},Dg.prototype.compareTo=function(t){var e=t;return this.compareDirection(e)},Dg.prototype.getDirectedCoordinate=function(){return this._p1},Dg.prototype.getDx=function(){return this._dx},Dg.prototype.getLabel=function(){return this._label},Dg.prototype.getEdge=function(){return this._edge},Dg.prototype.getQuadrant=function(){return this._quadrant},Dg.prototype.getNode=function(){return this._node},Dg.prototype.toString=function(){var t=Math.atan2(this._dy,this._dx),e=this.getClass().getName(),n=e.lastIndexOf(".");return"  "+e.substring(n+1)+": "+this._p0+" - "+this._p1+" "+this._quadrant+":"+t+"   "+this._label},Dg.prototype.computeLabel=function(t){},Dg.prototype.init=function(t,e){this._p0=t,this._p1=e,this._dx=e.x-t.x,this._dy=e.y-t.y,this._quadrant=Mg.quadrant(this._dx,this._dy),jf.isTrue(!(0===this._dx&&0===this._dy),"EdgeEnd with identical endpoints found")},Dg.prototype.interfaces_=function(){return[lf]},Dg.prototype.getClass=function(){return Dg};var Og=function(t){function e(){var e=arguments[0],n=arguments[1];if(t.call(this,e),this._isForward=null,this._isInResult=!1,this._isVisited=!1,this._sym=null,this._next=null,this._nextMin=null,this._edgeRing=null,this._minEdgeRing=null,this._depth=[0,-999,-999],this._isForward=n,n)this.init(e.getCoordinate(0),e.getCoordinate(1));else{var r=e.getNumPoints()-1;this.init(e.getCoordinate(r),e.getCoordinate(r-1))}this.computeDirectedLabel()}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.getNextMin=function(){return this._nextMin},e.prototype.getDepth=function(t){return this._depth[t]},e.prototype.setVisited=function(t){this._isVisited=t},e.prototype.computeDirectedLabel=function(){this._label=new Eg(this._edge.getLabel()),this._isForward||this._label.flip()},e.prototype.getNext=function(){return this._next},e.prototype.setDepth=function(t,e){if(-999!==this._depth[t]&&this._depth[t]!==e)throw new _g("assigned depths do not match",this.getCoordinate());this._depth[t]=e},e.prototype.isInteriorAreaEdge=function(){for(var t=this,e=!0,n=0;n<2;n++)t._label.isArea(n)&&t._label.getLocation(n,mg.LEFT)===yf.INTERIOR&&t._label.getLocation(n,mg.RIGHT)===yf.INTERIOR||(e=!1);return e},e.prototype.setNextMin=function(t){this._nextMin=t},e.prototype.print=function(e){t.prototype.print.call(this,e),e.print(" "+this._depth[mg.LEFT]+"/"+this._depth[mg.RIGHT]),e.print(" ("+this.getDepthDelta()+")"),this._isInResult&&e.print(" inResult")},e.prototype.setMinEdgeRing=function(t){this._minEdgeRing=t},e.prototype.isLineEdge=function(){var t=this._label.isLine(0)||this._label.isLine(1),e=!this._label.isArea(0)||this._label.allPositionsEqual(0,yf.EXTERIOR),n=!this._label.isArea(1)||this._label.allPositionsEqual(1,yf.EXTERIOR);return t&&e&&n},e.prototype.setEdgeRing=function(t){this._edgeRing=t},e.prototype.getMinEdgeRing=function(){return this._minEdgeRing},e.prototype.getDepthDelta=function(){var t=this._edge.getDepthDelta();return this._isForward||(t=-t),t},e.prototype.setInResult=function(t){this._isInResult=t},e.prototype.getSym=function(){return this._sym},e.prototype.isForward=function(){return this._isForward},e.prototype.getEdge=function(){return this._edge},e.prototype.printEdge=function(t){this.print(t),t.print(" "),this._isForward?this._edge.print(t):this._edge.printReverse(t)},e.prototype.setSym=function(t){this._sym=t},e.prototype.setVisitedEdge=function(t){this.setVisited(t),this._sym.setVisited(t)},e.prototype.setEdgeDepths=function(t,e){var n=this.getEdge().getDepthDelta();this._isForward||(n=-n);var r=1;t===mg.LEFT&&(r=-1);var o=mg.opposite(t),i=n*r,a=e+i;this.setDepth(t,e),this.setDepth(o,a)},e.prototype.getEdgeRing=function(){return this._edgeRing},e.prototype.isInResult=function(){return this._isInResult},e.prototype.setNext=function(t){this._next=t},e.prototype.isVisited=function(){return this._isVisited},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e.depthFactor=function(t,e){return t===yf.EXTERIOR&&e===yf.INTERIOR?1:t===yf.INTERIOR&&e===yf.EXTERIOR?-1:0},e}(Dg),Tg=function(){};Tg.prototype.createNode=function(t){return new Ag(t,null)},Tg.prototype.interfaces_=function(){return[]},Tg.prototype.getClass=function(){return Tg};var Rg=function(){if(this._edges=new fh,this._nodes=null,this._edgeEndList=new fh,0===arguments.length)this._nodes=new Ng(new Tg);else if(1===arguments.length){var t=arguments[0];this._nodes=new Ng(t)}};Rg.prototype.printEdges=function(t){var e=this;t.println("Edges:");for(var n=0;n<this._edges.size();n++){t.println("edge "+n+":");var r=e._edges.get(n);r.print(t),r.eiList.print(t)}},Rg.prototype.find=function(t){return this._nodes.find(t)},Rg.prototype.addNode=function(){if(arguments[0]instanceof Ag){var t=arguments[0];return this._nodes.addNode(t)}if(arguments[0]instanceof ff){var e=arguments[0];return this._nodes.addNode(e)}},Rg.prototype.getNodeIterator=function(){return this._nodes.iterator()},Rg.prototype.linkResultDirectedEdges=function(){for(var t=this._nodes.iterator();t.hasNext();){t.next().getEdges().linkResultDirectedEdges()}},Rg.prototype.debugPrintln=function(t){Of.out.println(t)},Rg.prototype.isBoundaryNode=function(t,e){var n=this._nodes.find(e);if(null===n)return!1;var r=n.getLabel();return null!==r&&r.getLocation(t)===yf.BOUNDARY},Rg.prototype.linkAllDirectedEdges=function(){for(var t=this._nodes.iterator();t.hasNext();){t.next().getEdges().linkAllDirectedEdges()}},Rg.prototype.matchInSameDirection=function(t,e,n,r){return!!t.equals(n)&&(Kf.computeOrientation(t,e,r)===Kf.COLLINEAR&&Mg.quadrant(t,e)===Mg.quadrant(n,r))},Rg.prototype.getEdgeEnds=function(){return this._edgeEndList},Rg.prototype.debugPrint=function(t){Of.out.print(t)},Rg.prototype.getEdgeIterator=function(){return this._edges.iterator()},Rg.prototype.findEdgeInSameDirection=function(t,e){for(var n=this,r=0;r<this._edges.size();r++){var o=n._edges.get(r),i=o.getCoordinates();if(n.matchInSameDirection(t,e,i[0],i[1]))return o;if(n.matchInSameDirection(t,e,i[i.length-1],i[i.length-2]))return o}return null},Rg.prototype.insertEdge=function(t){this._edges.add(t)},Rg.prototype.findEdgeEnd=function(t){for(var e=this.getEdgeEnds().iterator();e.hasNext();){var n=e.next();if(n.getEdge()===t)return n}return null},Rg.prototype.addEdges=function(t){for(var e=this,n=t.iterator();n.hasNext();){var r=n.next();e._edges.add(r);var o=new Og(r,!0),i=new Og(r,!1);o.setSym(i),i.setSym(o),e.add(o),e.add(i)}},Rg.prototype.add=function(t){this._nodes.add(t),this._edgeEndList.add(t)},Rg.prototype.getNodes=function(){return this._nodes.values()},Rg.prototype.findEdge=function(t,e){for(var n=this,r=0;r<this._edges.size();r++){var o=n._edges.get(r),i=o.getCoordinates();if(t.equals(i[0])&&e.equals(i[1]))return o}return null},Rg.prototype.interfaces_=function(){return[]},Rg.prototype.getClass=function(){return Rg},Rg.linkResultDirectedEdges=function(t){for(var e=t.iterator();e.hasNext();){e.next().getEdges().linkResultDirectedEdges()}};var Fg=function(){this._geometryFactory=null,this._shellList=new fh;var t=arguments[0];this._geometryFactory=t};Fg.prototype.sortShellsAndHoles=function(t,e,n){for(var r=t.iterator();r.hasNext();){var o=r.next();o.isHole()?n.add(o):e.add(o)}},Fg.prototype.computePolygons=function(t){for(var e=this,n=new fh,r=t.iterator();r.hasNext();){var o=r.next(),i=o.toPolygon(e._geometryFactory);n.add(i)}return n},Fg.prototype.placeFreeHoles=function(t,e){for(var n=this,r=e.iterator();r.hasNext();){var o=r.next();if(null===o.getShell()){var i=n.findEdgeRingContaining(o,t);if(null===i)throw new _g("unable to assign hole to a shell",o.getCoordinate(0));o.setShell(i)}}},Fg.prototype.buildMinimalEdgeRings=function(t,e,n){for(var r=this,o=new fh,i=t.iterator();i.hasNext();){var a=i.next();if(a.getMaxNodeDegree()>2){a.linkDirectedEdgesForMinimalEdgeRings();var s=a.buildMinimalRings(),c=r.findShell(s);null!==c?(r.placePolygonHoles(c,s),e.add(c)):n.addAll(s)}else o.add(a)}return o},Fg.prototype.containsPoint=function(t){for(var e=this._shellList.iterator();e.hasNext();){if(e.next().containsPoint(t))return!0}return!1},Fg.prototype.buildMaximalEdgeRings=function(t){for(var e=this,n=new fh,r=t.iterator();r.hasNext();){var o=r.next();if(o.isInResult()&&o.getLabel().isArea()&&null===o.getEdgeRing()){var i=new Ig(o,e._geometryFactory);n.add(i),i.setInResult()}}return n},Fg.prototype.placePolygonHoles=function(t,e){for(var n=e.iterator();n.hasNext();){var r=n.next();r.isHole()&&r.setShell(t)}},Fg.prototype.getPolygons=function(){return this.computePolygons(this._shellList)},Fg.prototype.findEdgeRingContaining=function(t,e){for(var n=t.getLinearRing(),r=n.getEnvelopeInternal(),o=n.getCoordinateN(0),i=null,a=null,s=e.iterator();s.hasNext();){var c=s.next(),u=c.getLinearRing(),l=u.getEnvelopeInternal();null!==i&&(a=i.getLinearRing().getEnvelopeInternal());var d=!1;l.contains(r)&&Kf.isPointInRing(o,u.getCoordinates())&&(d=!0),d&&(null===i||a.contains(l))&&(i=c)}return i},Fg.prototype.findShell=function(t){for(var e=0,n=null,r=t.iterator();r.hasNext();){var o=r.next();o.isHole()||(n=o,e++)}return jf.isTrue(e<=1,"found two shells in MinimalEdgeRing list"),n},Fg.prototype.add=function(){if(1===arguments.length){var t=arguments[0];this.add(t.getEdgeEnds(),t.getNodes())}else if(2===arguments.length){var e=arguments[0],n=arguments[1];Rg.linkResultDirectedEdges(n);var r=this.buildMaximalEdgeRings(e),o=new fh,i=this.buildMinimalEdgeRings(r,this._shellList,o);this.sortShellsAndHoles(i,this._shellList,o),this.placeFreeHoles(this._shellList,o)}},Fg.prototype.interfaces_=function(){return[]},Fg.prototype.getClass=function(){return Fg};var Gg=function(){};Gg.prototype.getBounds=function(){},Gg.prototype.interfaces_=function(){return[]},Gg.prototype.getClass=function(){return Gg};var qg=function(){this._bounds=null,this._item=null;var t=arguments[0],e=arguments[1];this._bounds=t,this._item=e};qg.prototype.getItem=function(){return this._item},qg.prototype.getBounds=function(){return this._bounds},qg.prototype.interfaces_=function(){return[Gg,ic]},qg.prototype.getClass=function(){return qg};var kg=function(){this._size=null,this._items=null,this._size=0,this._items=new fh,this._items.add(null)};kg.prototype.poll=function(){if(this.isEmpty())return null;var t=this._items.get(1);return this._items.set(1,this._items.get(this._size)),this._size-=1,this.reorder(1),t},kg.prototype.size=function(){return this._size},kg.prototype.reorder=function(t){for(var e=this,n=null,r=this._items.get(t);2*t<=this._size&&(n=2*t,n!==e._size&&e._items.get(n+1).compareTo(e._items.get(n))<0&&n++,e._items.get(n).compareTo(r)<0);t=n)e._items.set(t,e._items.get(n));this._items.set(t,r)},kg.prototype.clear=function(){this._size=0,this._items.clear()},kg.prototype.isEmpty=function(){return 0===this._size},kg.prototype.add=function(t){var e=this;this._items.add(null),this._size+=1;var n=this._size;for(this._items.set(0,t);t.compareTo(this._items.get(Math.trunc(n/2)))<0;n/=2)e._items.set(n,e._items.get(Math.trunc(n/2)));this._items.set(n,t)},kg.prototype.interfaces_=function(){return[]},kg.prototype.getClass=function(){return kg};var Ug=function(){};Ug.prototype.visitItem=function(t){},Ug.prototype.interfaces_=function(){return[]},Ug.prototype.getClass=function(){return Ug};var Bg=function(){};Bg.prototype.insert=function(t,e){},Bg.prototype.remove=function(t,e){},Bg.prototype.query=function(){},Bg.prototype.interfaces_=function(){return[]},Bg.prototype.getClass=function(){return Bg};var Vg=function(){if(this._childBoundables=new fh,this._bounds=null,this._level=null,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this._level=t}},zg={serialVersionUID:{configurable:!0}};Vg.prototype.getLevel=function(){return this._level},Vg.prototype.size=function(){return this._childBoundables.size()},Vg.prototype.getChildBoundables=function(){return this._childBoundables},Vg.prototype.addChildBoundable=function(t){jf.isTrue(null===this._bounds),this._childBoundables.add(t)},Vg.prototype.isEmpty=function(){return this._childBoundables.isEmpty()},Vg.prototype.getBounds=function(){return null===this._bounds&&(this._bounds=this.computeBounds()),this._bounds},Vg.prototype.interfaces_=function(){return[Gg,ic]},Vg.prototype.getClass=function(){return Vg},zg.serialVersionUID.get=function(){return 0x5a1e55ec41369800},Object.defineProperties(Vg,zg);var jg=function(){};jg.reverseOrder=function(){return{compare:function(t,e){return e.compareTo(t)}}},jg.min=function(t){return jg.sort(t),t.get(0)},jg.sort=function(t,e){var n=t.toArray();e?Ph.sort(n,e):Ph.sort(n);for(var r=t.iterator(),o=0,i=n.length;o<i;o++)r.next(),r.set(n[o])},jg.singletonList=function(t){var e=new fh;return e.add(t),e};var Yg=function(){this._boundable1=null,this._boundable2=null,this._distance=null,this._itemDistance=null;var t=arguments[0],e=arguments[1],n=arguments[2];this._boundable1=t,this._boundable2=e,this._itemDistance=n,this._distance=this.distance()};Yg.prototype.expandToQueue=function(t,e){var n=Yg.isComposite(this._boundable1),r=Yg.isComposite(this._boundable2);if(n&&r)return Yg.area(this._boundable1)>Yg.area(this._boundable2)?(this.expand(this._boundable1,this._boundable2,t,e),null):(this.expand(this._boundable2,this._boundable1,t,e),null);if(n)return this.expand(this._boundable1,this._boundable2,t,e),null;if(r)return this.expand(this._boundable2,this._boundable1,t,e),null;throw new sf("neither boundable is composite")},Yg.prototype.isLeaves=function(){return!(Yg.isComposite(this._boundable1)||Yg.isComposite(this._boundable2))},Yg.prototype.compareTo=function(t){var e=t;return this._distance<e._distance?-1:this._distance>e._distance?1:0},Yg.prototype.expand=function(t,e,n,r){for(var o=this,i=t.getChildBoundables(),a=i.iterator();a.hasNext();){var s=a.next(),c=new Yg(s,e,o._itemDistance);c.getDistance()<r&&n.add(c)}},Yg.prototype.getBoundable=function(t){return 0===t?this._boundable1:this._boundable2},Yg.prototype.getDistance=function(){return this._distance},Yg.prototype.distance=function(){return this.isLeaves()?this._itemDistance.distance(this._boundable1,this._boundable2):this._boundable1.getBounds().distance(this._boundable2.getBounds())},Yg.prototype.interfaces_=function(){return[lf]},Yg.prototype.getClass=function(){return Yg},Yg.area=function(t){return t.getBounds().getArea()},Yg.isComposite=function(t){return t instanceof Vg};var Xg=function t(){if(this._root=null,this._built=!1,this._itemBoundables=new fh,this._nodeCapacity=null,0===arguments.length){var e=t.DEFAULT_NODE_CAPACITY;this._nodeCapacity=e}else if(1===arguments.length){var n=arguments[0];jf.isTrue(n>1,"Node capacity must be greater than 1"),this._nodeCapacity=n}},Wg={IntersectsOp:{configurable:!0},serialVersionUID:{configurable:!0},DEFAULT_NODE_CAPACITY:{configurable:!0}};Xg.prototype.getNodeCapacity=function(){return this._nodeCapacity},Xg.prototype.lastNode=function(t){return t.get(t.size()-1)},Xg.prototype.size=function(){var t=this;if(0===arguments.length)return this.isEmpty()?0:(this.build(),this.size(this._root));if(1===arguments.length){for(var e=arguments[0],n=0,r=e.getChildBoundables().iterator();r.hasNext();){var o=r.next();o instanceof Vg?n+=t.size(o):o instanceof qg&&(n+=1)}return n}},Xg.prototype.removeItem=function(t,e){for(var n=null,r=t.getChildBoundables().iterator();r.hasNext();){
var o=r.next();o instanceof qg&&o.getItem()===e&&(n=o)}return null!==n&&(t.getChildBoundables().remove(n),!0)},Xg.prototype.itemsTree=function(){var t=this;if(0===arguments.length){this.build();var e=this.itemsTree(this._root);return null===e?new fh:e}if(1===arguments.length){for(var n=arguments[0],r=new fh,o=n.getChildBoundables().iterator();o.hasNext();){var i=o.next();if(i instanceof Vg){var a=t.itemsTree(i);null!==a&&r.add(a)}else i instanceof qg?r.add(i.getItem()):jf.shouldNeverReachHere()}return r.size()<=0?null:r}},Xg.prototype.insert=function(t,e){jf.isTrue(!this._built,"Cannot insert items into an STR packed R-tree after it has been built."),this._itemBoundables.add(new qg(t,e))},Xg.prototype.boundablesAtLevel=function(){var t=this;if(1===arguments.length){var e=arguments[0],n=new fh;return this.boundablesAtLevel(e,this._root,n),n}if(3===arguments.length){var r=arguments[0],o=arguments[1],i=arguments[2];if(jf.isTrue(r>-2),o.getLevel()===r)return i.add(o),null;for(var a=o.getChildBoundables().iterator();a.hasNext();){var s=a.next();s instanceof Vg?t.boundablesAtLevel(r,s,i):(jf.isTrue(s instanceof qg),-1===r&&i.add(s))}return null}},Xg.prototype.query=function(){var t=this;if(1===arguments.length){var e=arguments[0];this.build();var n=new fh;return this.isEmpty()?n:(this.getIntersectsOp().intersects(this._root.getBounds(),e)&&this.query(e,this._root,n),n)}if(2===arguments.length){var r=arguments[0],o=arguments[1];if(this.build(),this.isEmpty())return null;this.getIntersectsOp().intersects(this._root.getBounds(),r)&&this.query(r,this._root,o)}else if(3===arguments.length)if(_f(arguments[2],Ug)&&arguments[0]instanceof Object&&arguments[1]instanceof Vg)for(var i=arguments[0],a=arguments[1],s=arguments[2],c=a.getChildBoundables(),u=0;u<c.size();u++){var l=c.get(u);t.getIntersectsOp().intersects(l.getBounds(),i)&&(l instanceof Vg?t.query(i,l,s):l instanceof qg?s.visitItem(l.getItem()):jf.shouldNeverReachHere())}else if(_f(arguments[2],ph)&&arguments[0]instanceof Object&&arguments[1]instanceof Vg)for(var d=arguments[0],p=arguments[1],f=arguments[2],h=p.getChildBoundables(),g=0;g<h.size();g++){var m=h.get(g);t.getIntersectsOp().intersects(m.getBounds(),d)&&(m instanceof Vg?t.query(d,m,f):m instanceof qg?f.add(m.getItem()):jf.shouldNeverReachHere())}},Xg.prototype.build=function(){if(this._built)return null;this._root=this._itemBoundables.isEmpty()?this.createNode(0):this.createHigherLevels(this._itemBoundables,-1),this._itemBoundables=null,this._built=!0},Xg.prototype.getRoot=function(){return this.build(),this._root},Xg.prototype.remove=function(){var t=this;if(2===arguments.length){var e=arguments[0],n=arguments[1];return this.build(),!!this.getIntersectsOp().intersects(this._root.getBounds(),e)&&this.remove(e,this._root,n)}if(3===arguments.length){var r=arguments[0],o=arguments[1],i=arguments[2],a=this.removeItem(o,i);if(a)return!0;for(var s=null,c=o.getChildBoundables().iterator();c.hasNext();){var u=c.next();if(t.getIntersectsOp().intersects(u.getBounds(),r)&&(u instanceof Vg&&(a=t.remove(r,u,i)))){s=u;break}}return null!==s&&s.getChildBoundables().isEmpty()&&o.getChildBoundables().remove(s),a}},Xg.prototype.createHigherLevels=function(t,e){jf.isTrue(!t.isEmpty());var n=this.createParentBoundables(t,e+1);return 1===n.size()?n.get(0):this.createHigherLevels(n,e+1)},Xg.prototype.depth=function(){var t=this;if(0===arguments.length)return this.isEmpty()?0:(this.build(),this.depth(this._root));if(1===arguments.length){for(var e=arguments[0],n=0,r=e.getChildBoundables().iterator();r.hasNext();){var o=r.next();if(o instanceof Vg){var i=t.depth(o);i>n&&(n=i)}}return n+1}},Xg.prototype.createParentBoundables=function(t,e){var n=this;jf.isTrue(!t.isEmpty());var r=new fh;r.add(this.createNode(e));var o=new fh(t);jg.sort(o,this.getComparator());for(var i=o.iterator();i.hasNext();){var a=i.next();n.lastNode(r).getChildBoundables().size()===n.getNodeCapacity()&&r.add(n.createNode(e)),n.lastNode(r).addChildBoundable(a)}return r},Xg.prototype.isEmpty=function(){return this._built?this._root.isEmpty():this._itemBoundables.isEmpty()},Xg.prototype.interfaces_=function(){return[ic]},Xg.prototype.getClass=function(){return Xg},Xg.compareDoubles=function(t,e){return t>e?1:t<e?-1:0},Wg.IntersectsOp.get=function(){return $g},Wg.serialVersionUID.get=function(){return-0x35ef64c82d4c5400},Wg.DEFAULT_NODE_CAPACITY.get=function(){return 10},Object.defineProperties(Xg,Wg);var $g=function(){},Hg=function(){};Hg.prototype.distance=function(t,e){},Hg.prototype.interfaces_=function(){return[]},Hg.prototype.getClass=function(){return Hg};var Kg=function(t){function e(n){n=n||e.DEFAULT_NODE_CAPACITY,t.call(this,n)}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={STRtreeNode:{configurable:!0},serialVersionUID:{configurable:!0},xComparator:{configurable:!0},yComparator:{configurable:!0},intersectsOp:{configurable:!0},DEFAULT_NODE_CAPACITY:{configurable:!0}};return e.prototype.createParentBoundablesFromVerticalSlices=function(t,e){var n=this;jf.isTrue(t.length>0);for(var r=new fh,o=0;o<t.length;o++)r.addAll(n.createParentBoundablesFromVerticalSlice(t[o],e));return r},e.prototype.createNode=function(t){return new Jg(t)},e.prototype.size=function(){return 0===arguments.length?t.prototype.size.call(this):t.prototype.size.apply(this,arguments)},e.prototype.insert=function(){if(2!==arguments.length)return t.prototype.insert.apply(this,arguments);var e=arguments[0],n=arguments[1];if(e.isNull())return null;t.prototype.insert.call(this,e,n)},e.prototype.getIntersectsOp=function(){return e.intersectsOp},e.prototype.verticalSlices=function(t,e){for(var n=Math.trunc(Math.ceil(t.size()/e)),r=new Array(e).fill(null),o=t.iterator(),i=0;i<e;i++){r[i]=new fh;for(var a=0;o.hasNext()&&a<n;){var s=o.next();r[i].add(s),a++}}return r},e.prototype.query=function(){if(1===arguments.length){var e=arguments[0];return t.prototype.query.call(this,e)}if(2===arguments.length){var n=arguments[0],r=arguments[1];t.prototype.query.call(this,n,r)}else if(3===arguments.length)if(_f(arguments[2],Ug)&&arguments[0]instanceof Object&&arguments[1]instanceof Vg){var o=arguments[0],i=arguments[1],a=arguments[2];t.prototype.query.call(this,o,i,a)}else if(_f(arguments[2],ph)&&arguments[0]instanceof Object&&arguments[1]instanceof Vg){var s=arguments[0],c=arguments[1],u=arguments[2];t.prototype.query.call(this,s,c,u)}},e.prototype.getComparator=function(){return e.yComparator},e.prototype.createParentBoundablesFromVerticalSlice=function(e,n){return t.prototype.createParentBoundables.call(this,e,n)},e.prototype.remove=function(){if(2===arguments.length){var e=arguments[0],n=arguments[1];return t.prototype.remove.call(this,e,n)}return t.prototype.remove.apply(this,arguments)},e.prototype.depth=function(){return 0===arguments.length?t.prototype.depth.call(this):t.prototype.depth.apply(this,arguments)},e.prototype.createParentBoundables=function(t,n){jf.isTrue(!t.isEmpty());var r=Math.trunc(Math.ceil(t.size()/this.getNodeCapacity())),o=new fh(t);jg.sort(o,e.xComparator);var i=this.verticalSlices(o,Math.trunc(Math.ceil(Math.sqrt(r))));return this.createParentBoundablesFromVerticalSlices(i,n)},e.prototype.nearestNeighbour=function(){if(1===arguments.length){if(_f(arguments[0],Hg)){var t=arguments[0],n=new Yg(this.getRoot(),this.getRoot(),t);return this.nearestNeighbour(n)}if(arguments[0]instanceof Yg){var r=arguments[0];return this.nearestNeighbour(r,cf.POSITIVE_INFINITY)}}else if(2===arguments.length){if(arguments[0]instanceof e&&_f(arguments[1],Hg)){var o=arguments[0],i=arguments[1],a=new Yg(this.getRoot(),o.getRoot(),i);return this.nearestNeighbour(a)}if(arguments[0]instanceof Yg&&"number"==typeof arguments[1]){var s=arguments[0],c=arguments[1],u=c,l=null,d=new kg;for(d.add(s);!d.isEmpty()&&u>0;){var p=d.poll(),f=p.getDistance();if(f>=u)break;p.isLeaves()?(u=f,l=p):p.expandToQueue(d,u)}return[l.getBoundable(0).getItem(),l.getBoundable(1).getItem()]}}else if(3===arguments.length){var h=arguments[0],g=arguments[1],m=arguments[2],y=new qg(h,g),v=new Yg(this.getRoot(),y,m);return this.nearestNeighbour(v)[0]}},e.prototype.interfaces_=function(){return[Bg,ic]},e.prototype.getClass=function(){return e},e.centreX=function(t){return e.avg(t.getMinX(),t.getMaxX())},e.avg=function(t,e){return(t+e)/2},e.centreY=function(t){return e.avg(t.getMinY(),t.getMaxY())},n.STRtreeNode.get=function(){return Jg},n.serialVersionUID.get=function(){return 0x39920f7d5f261e0},n.xComparator.get=function(){return{interfaces_:function(){return[pf]},compare:function(n,r){return t.compareDoubles(e.centreX(n.getBounds()),e.centreX(r.getBounds()))}}},n.yComparator.get=function(){return{interfaces_:function(){return[pf]},compare:function(n,r){return t.compareDoubles(e.centreY(n.getBounds()),e.centreY(r.getBounds()))}}},n.intersectsOp.get=function(){return{interfaces_:function(){return[t.IntersectsOp]},intersects:function(t,e){return t.intersects(e)}}},n.DEFAULT_NODE_CAPACITY.get=function(){return 10},Object.defineProperties(e,n),e}(Xg),Jg=function(t){function e(){var e=arguments[0];t.call(this,e)}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.computeBounds=function(){for(var t=null,e=this.getChildBoundables().iterator();e.hasNext();){var n=e.next();null===t?t=new Rf(n.getBounds()):t.expandToInclude(n.getBounds())}return t},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(Vg),Zg=function(){};Zg.prototype.interfaces_=function(){return[]},Zg.prototype.getClass=function(){return Zg},Zg.relativeSign=function(t,e){return t<e?-1:t>e?1:0},Zg.compare=function(t,e,n){if(e.equals2D(n))return 0;var r=Zg.relativeSign(e.x,n.x),o=Zg.relativeSign(e.y,n.y);switch(t){case 0:return Zg.compareValue(r,o);case 1:return Zg.compareValue(o,r);case 2:return Zg.compareValue(o,-r);case 3:return Zg.compareValue(-r,o);case 4:return Zg.compareValue(-r,-o);case 5:return Zg.compareValue(-o,-r);case 6:return Zg.compareValue(-o,r);case 7:return Zg.compareValue(r,-o)}return jf.shouldNeverReachHere("invalid octant value"),0},Zg.compareValue=function(t,e){return t<0?-1:t>0?1:e<0?-1:e>0?1:0};var Qg=function(){this._segString=null,this.coord=null,this.segmentIndex=null,this._segmentOctant=null,this._isInterior=null;var t=arguments[0],e=arguments[1],n=arguments[2],r=arguments[3];this._segString=t,this.coord=new ff(e),this.segmentIndex=n,this._segmentOctant=r,this._isInterior=!e.equals2D(t.getCoordinate(n))};Qg.prototype.getCoordinate=function(){return this.coord},Qg.prototype.print=function(t){t.print(this.coord),t.print(" seg # = "+this.segmentIndex)},Qg.prototype.compareTo=function(t){var e=t;return this.segmentIndex<e.segmentIndex?-1:this.segmentIndex>e.segmentIndex?1:this.coord.equals2D(e.coord)?0:Zg.compare(this._segmentOctant,this.coord,e.coord)},Qg.prototype.isEndPoint=function(t){return 0===this.segmentIndex&&!this._isInterior||this.segmentIndex===t},Qg.prototype.isInterior=function(){return this._isInterior},Qg.prototype.interfaces_=function(){return[lf]},Qg.prototype.getClass=function(){return Qg};var tm=function(){this._nodeMap=new hc,this._edge=null;var t=arguments[0];this._edge=t};tm.prototype.getSplitCoordinates=function(){var t=this,e=new gh;this.addEndpoints();for(var n=this.iterator(),r=n.next();n.hasNext();){var o=n.next();t.addEdgeCoordinates(r,o,e),r=o}return e.toCoordinateArray()},tm.prototype.addCollapsedNodes=function(){var t=this,e=new fh;this.findCollapsesFromInsertedNodes(e),this.findCollapsesFromExistingVertices(e);for(var n=e.iterator();n.hasNext();){var r=n.next().intValue();t.add(t._edge.getCoordinate(r),r)}},tm.prototype.print=function(t){t.println("Intersections:");for(var e=this.iterator();e.hasNext();){e.next().print(t)}},tm.prototype.findCollapsesFromExistingVertices=function(t){for(var e=this,n=0;n<this._edge.size()-2;n++){var r=e._edge.getCoordinate(n),o=e._edge.getCoordinate(n+2);r.equals2D(o)&&t.add(new Ef(n+1))}},tm.prototype.addEdgeCoordinates=function(t,e,n){var r=this,o=this._edge.getCoordinate(e.segmentIndex),i=e.isInterior()||!e.coord.equals2D(o);n.add(new ff(t.coord),!1);for(var a=t.segmentIndex+1;a<=e.segmentIndex;a++)n.add(r._edge.getCoordinate(a));i&&n.add(new ff(e.coord))},tm.prototype.iterator=function(){return this._nodeMap.values().iterator()},tm.prototype.addSplitEdges=function(t){var e=this;this.addEndpoints(),this.addCollapsedNodes();for(var n=this.iterator(),r=n.next();n.hasNext();){var o=n.next(),i=e.createSplitEdge(r,o);t.add(i),r=o}},tm.prototype.findCollapseIndex=function(t,e,n){if(!t.coord.equals2D(e.coord))return!1;var r=e.segmentIndex-t.segmentIndex;return e.isInterior()||r--,1===r&&(n[0]=t.segmentIndex+1,!0)},tm.prototype.findCollapsesFromInsertedNodes=function(t){for(var e=this,n=new Array(1).fill(null),r=this.iterator(),o=r.next();r.hasNext();){var i=r.next();e.findCollapseIndex(o,i,n)&&t.add(new Ef(n[0])),o=i}},tm.prototype.getEdge=function(){return this._edge},tm.prototype.addEndpoints=function(){var t=this._edge.size()-1;this.add(this._edge.getCoordinate(0),0),this.add(this._edge.getCoordinate(t),t)},tm.prototype.createSplitEdge=function(t,e){var n=this,r=e.segmentIndex-t.segmentIndex+2,o=this._edge.getCoordinate(e.segmentIndex),i=e.isInterior()||!e.coord.equals2D(o);i||r--;var a=new Array(r).fill(null),s=0;a[s++]=new ff(t.coord);for(var c=t.segmentIndex+1;c<=e.segmentIndex;c++)a[s++]=n._edge.getCoordinate(c);return i&&(a[s]=new ff(e.coord)),new om(a,this._edge.getData())},tm.prototype.add=function(t,e){var n=new Qg(this._edge,t,e,this._edge.getSegmentOctant(e)),r=this._nodeMap.get(n);return null!==r?(jf.isTrue(r.coord.equals2D(t),"Found equal nodes with different coordinates"),r):(this._nodeMap.put(n,n),n)},tm.prototype.checkSplitEdgesCorrectness=function(t){var e=this._edge.getCoordinates(),n=t.get(0),r=n.getCoordinate(0);if(!r.equals2D(e[0]))throw new Vf("bad split edge start point at "+r);var o=t.get(t.size()-1),i=o.getCoordinates(),a=i[i.length-1];if(!a.equals2D(e[e.length-1]))throw new Vf("bad split edge end point at "+a)},tm.prototype.interfaces_=function(){return[]},tm.prototype.getClass=function(){return tm};var em=function(){};em.prototype.interfaces_=function(){return[]},em.prototype.getClass=function(){return em},em.octant=function(){if("number"==typeof arguments[0]&&"number"==typeof arguments[1]){var t=arguments[0],e=arguments[1];if(0===t&&0===e)throw new sf("Cannot compute the octant for point ( "+t+", "+e+" )");var n=Math.abs(t),r=Math.abs(e);return t>=0?e>=0?n>=r?0:1:n>=r?7:6:e>=0?n>=r?3:2:n>=r?4:5}if(arguments[0]instanceof ff&&arguments[1]instanceof ff){var o=arguments[0],i=arguments[1],a=i.x-o.x,s=i.y-o.y;if(0===a&&0===s)throw new sf("Cannot compute the octant for two identical points "+o);return em.octant(a,s)}};var nm=function(){};nm.prototype.getCoordinates=function(){},nm.prototype.size=function(){},nm.prototype.getCoordinate=function(t){},nm.prototype.isClosed=function(){},nm.prototype.setData=function(t){},nm.prototype.getData=function(){},nm.prototype.interfaces_=function(){return[]},nm.prototype.getClass=function(){return nm};var rm=function(){};rm.prototype.addIntersection=function(t,e){},rm.prototype.interfaces_=function(){return[nm]},rm.prototype.getClass=function(){return rm};var om=function(){this._nodeList=new tm(this),this._pts=null,this._data=null;var t=arguments[0],e=arguments[1];this._pts=t,this._data=e};om.prototype.getCoordinates=function(){return this._pts},om.prototype.size=function(){return this._pts.length},om.prototype.getCoordinate=function(t){return this._pts[t]},om.prototype.isClosed=function(){return this._pts[0].equals(this._pts[this._pts.length-1])},om.prototype.getSegmentOctant=function(t){return t===this._pts.length-1?-1:this.safeOctant(this.getCoordinate(t),this.getCoordinate(t+1))},om.prototype.setData=function(t){this._data=t},om.prototype.safeOctant=function(t,e){return t.equals2D(e)?0:em.octant(t,e)},om.prototype.getData=function(){return this._data},om.prototype.addIntersection=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];this.addIntersectionNode(t,e)}else if(4===arguments.length){var n=arguments[0],r=arguments[1],o=arguments[3],i=new ff(n.getIntersection(o));this.addIntersection(i,r)}},om.prototype.toString=function(){return Bf.toLineString(new Qh(this._pts))},om.prototype.getNodeList=function(){return this._nodeList},om.prototype.addIntersectionNode=function(t,e){var n=e,r=n+1;if(r<this._pts.length){var o=this._pts[r];t.equals2D(o)&&(n=r)}return this._nodeList.add(t,n)},om.prototype.addIntersections=function(t,e,n){for(var r=this,o=0;o<t.getIntersectionNum();o++)r.addIntersection(t,e,n,o)},om.prototype.interfaces_=function(){return[rm]},om.prototype.getClass=function(){return om},om.getNodedSubstrings=function(){if(1===arguments.length){var t=arguments[0],e=new fh;return om.getNodedSubstrings(t,e),e}if(2===arguments.length)for(var n=arguments[0],r=arguments[1],o=n.iterator();o.hasNext();){var i=o.next();i.getNodeList().addSplitEdges(r)}};var im=function(){if(this.p0=null,this.p1=null,0===arguments.length)this.p0=new ff,this.p1=new ff;else if(1===arguments.length){var t=arguments[0];this.p0=new ff(t.p0),this.p1=new ff(t.p1)}else if(2===arguments.length)this.p0=arguments[0],this.p1=arguments[1];else if(4===arguments.length){var e=arguments[0],n=arguments[1],r=arguments[2],o=arguments[3];this.p0=new ff(e,n),this.p1=new ff(r,o)}},am={serialVersionUID:{configurable:!0}};im.prototype.minX=function(){return Math.min(this.p0.x,this.p1.x)},im.prototype.orientationIndex=function(){if(arguments[0]instanceof im){var t=arguments[0],e=Kf.orientationIndex(this.p0,this.p1,t.p0),n=Kf.orientationIndex(this.p0,this.p1,t.p1);return e>=0&&n>=0?Math.max(e,n):e<=0&&n<=0?Math.max(e,n):0}if(arguments[0]instanceof ff){var r=arguments[0];return Kf.orientationIndex(this.p0,this.p1,r)}},im.prototype.toGeometry=function(t){return t.createLineString([this.p0,this.p1])},im.prototype.isVertical=function(){return this.p0.x===this.p1.x},im.prototype.equals=function(t){if(!(t instanceof im))return!1;var e=t;return this.p0.equals(e.p0)&&this.p1.equals(e.p1)},im.prototype.intersection=function(t){var e=new Wf;return e.computeIntersection(this.p0,this.p1,t.p0,t.p1),e.hasIntersection()?e.getIntersection(0):null},im.prototype.project=function(){if(arguments[0]instanceof ff){var t=arguments[0];if(t.equals(this.p0)||t.equals(this.p1))return new ff(t);var e=this.projectionFactor(t),n=new ff;return n.x=this.p0.x+e*(this.p1.x-this.p0.x),n.y=this.p0.y+e*(this.p1.y-this.p0.y),n}if(arguments[0]instanceof im){var r=arguments[0],o=this.projectionFactor(r.p0),i=this.projectionFactor(r.p1);if(o>=1&&i>=1)return null;if(o<=0&&i<=0)return null;var a=this.project(r.p0);o<0&&(a=this.p0),o>1&&(a=this.p1);var s=this.project(r.p1);return i<0&&(s=this.p0),i>1&&(s=this.p1),new im(a,s)}},im.prototype.normalize=function(){this.p1.compareTo(this.p0)<0&&this.reverse()},im.prototype.angle=function(){return Math.atan2(this.p1.y-this.p0.y,this.p1.x-this.p0.x)},im.prototype.getCoordinate=function(t){return 0===t?this.p0:this.p1},im.prototype.distancePerpendicular=function(t){return Kf.distancePointLinePerpendicular(t,this.p0,this.p1)},im.prototype.minY=function(){return Math.min(this.p0.y,this.p1.y)},im.prototype.midPoint=function(){return im.midPoint(this.p0,this.p1)},im.prototype.projectionFactor=function(t){if(t.equals(this.p0))return 0;if(t.equals(this.p1))return 1;var e=this.p1.x-this.p0.x,n=this.p1.y-this.p0.y,r=e*e+n*n;return r<=0?cf.NaN:((t.x-this.p0.x)*e+(t.y-this.p0.y)*n)/r},im.prototype.closestPoints=function(t){var e=this.intersection(t);if(null!==e)return[e,e];var n=new Array(2).fill(null),r=cf.MAX_VALUE,o=null,i=this.closestPoint(t.p0);r=i.distance(t.p0),n[0]=i,n[1]=t.p0;var a=this.closestPoint(t.p1);(o=a.distance(t.p1))<r&&(r=o,n[0]=a,n[1]=t.p1);var s=t.closestPoint(this.p0);(o=s.distance(this.p0))<r&&(r=o,n[0]=this.p0,n[1]=s);var c=t.closestPoint(this.p1);return o=c.distance(this.p1),o<r&&(r=o,n[0]=this.p1,n[1]=c),n},im.prototype.closestPoint=function(t){var e=this.projectionFactor(t);return e>0&&e<1?this.project(t):this.p0.distance(t)<this.p1.distance(t)?this.p0:this.p1},im.prototype.maxX=function(){return Math.max(this.p0.x,this.p1.x)},im.prototype.getLength=function(){return this.p0.distance(this.p1)},im.prototype.compareTo=function(t){var e=t,n=this.p0.compareTo(e.p0);return 0!==n?n:this.p1.compareTo(e.p1)},im.prototype.reverse=function(){var t=this.p0;this.p0=this.p1,this.p1=t},im.prototype.equalsTopo=function(t){return this.p0.equals(t.p0)&&(this.p1.equals(t.p1)||this.p0.equals(t.p1))&&this.p1.equals(t.p0)},im.prototype.lineIntersection=function(t){try{return Tf.intersection(this.p0,this.p1,t.p0,t.p1)}catch(t){if(!(t instanceof Df))throw t}return null},im.prototype.maxY=function(){return Math.max(this.p0.y,this.p1.y)},im.prototype.pointAlongOffset=function(t,e){var n=this.p0.x+t*(this.p1.x-this.p0.x),r=this.p0.y+t*(this.p1.y-this.p0.y),o=this.p1.x-this.p0.x,i=this.p1.y-this.p0.y,a=Math.sqrt(o*o+i*i),s=0,c=0;if(0!==e){if(a<=0)throw new Error("Cannot compute offset from zero-length line segment");s=e*o/a,c=e*i/a}return new ff(n-c,r+s)},im.prototype.setCoordinates=function(){if(1===arguments.length){var t=arguments[0];this.setCoordinates(t.p0,t.p1)}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this.p0.x=e.x,this.p0.y=e.y,this.p1.x=n.x,this.p1.y=n.y}},im.prototype.segmentFraction=function(t){var e=this.projectionFactor(t);return e<0?e=0:(e>1||cf.isNaN(e))&&(e=1),e},im.prototype.toString=function(){return"LINESTRING( "+this.p0.x+" "+this.p0.y+", "+this.p1.x+" "+this.p1.y+")"},im.prototype.isHorizontal=function(){return this.p0.y===this.p1.y},im.prototype.distance=function(){if(arguments[0]instanceof im){var t=arguments[0];return Kf.distanceLineLine(this.p0,this.p1,t.p0,t.p1)}if(arguments[0]instanceof ff){var e=arguments[0];return Kf.distancePointLine(e,this.p0,this.p1)}},im.prototype.pointAlong=function(t){var e=new ff;return e.x=this.p0.x+t*(this.p1.x-this.p0.x),e.y=this.p0.y+t*(this.p1.y-this.p0.y),e},im.prototype.hashCode=function(){var t=cf.doubleToLongBits(this.p0.x);t^=31*cf.doubleToLongBits(this.p0.y);var e=Math.trunc(t)^Math.trunc(t>>32),n=cf.doubleToLongBits(this.p1.x);return n^=31*cf.doubleToLongBits(this.p1.y),e^Math.trunc(n)^Math.trunc(n>>32)},im.prototype.interfaces_=function(){return[lf,ic]},im.prototype.getClass=function(){return im},im.midPoint=function(t,e){return new ff((t.x+e.x)/2,(t.y+e.y)/2)},am.serialVersionUID.get=function(){return 0x2d2172135f411c00},Object.defineProperties(im,am);var sm=function(){this.tempEnv1=new Rf,this.tempEnv2=new Rf,this._overlapSeg1=new im,this._overlapSeg2=new im};sm.prototype.overlap=function(){if(2===arguments.length);else if(4===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],r=arguments[3];t.getLineSegment(e,this._overlapSeg1),n.getLineSegment(r,this._overlapSeg2),this.overlap(this._overlapSeg1,this._overlapSeg2)}},sm.prototype.interfaces_=function(){return[]},sm.prototype.getClass=function(){return sm};var cm=function(){this._pts=null,this._start=null,this._end=null,this._env=null,this._context=null,this._id=null;var t=arguments[0],e=arguments[1],n=arguments[2],r=arguments[3];this._pts=t,this._start=e,this._end=n,this._context=r};cm.prototype.getLineSegment=function(t,e){e.p0=this._pts[t],e.p1=this._pts[t+1]},cm.prototype.computeSelect=function(t,e,n,r){var o=this._pts[e],i=this._pts[n];if(r.tempEnv1.init(o,i),n-e==1)return r.select(this,e),null;if(!t.intersects(r.tempEnv1))return null;var a=Math.trunc((e+n)/2);e<a&&this.computeSelect(t,e,a,r),a<n&&this.computeSelect(t,a,n,r)},cm.prototype.getCoordinates=function(){for(var t=this,e=new Array(this._end-this._start+1).fill(null),n=0,r=this._start;r<=this._end;r++)e[n++]=t._pts[r];return e},cm.prototype.computeOverlaps=function(t,e){this.computeOverlapsInternal(this._start,this._end,t,t._start,t._end,e)},cm.prototype.setId=function(t){this._id=t},cm.prototype.select=function(t,e){this.computeSelect(t,this._start,this._end,e)},cm.prototype.getEnvelope=function(){if(null===this._env){var t=this._pts[this._start],e=this._pts[this._end];this._env=new Rf(t,e)}return this._env},cm.prototype.getEndIndex=function(){return this._end},cm.prototype.getStartIndex=function(){return this._start},cm.prototype.getContext=function(){return this._context},cm.prototype.getId=function(){return this._id},cm.prototype.computeOverlapsInternal=function(t,e,n,r,o,i){var a=this._pts[t],s=this._pts[e],c=n._pts[r],u=n._pts[o];if(e-t==1&&o-r==1)return i.overlap(this,t,n,r),null;if(i.tempEnv1.init(a,s),i.tempEnv2.init(c,u),!i.tempEnv1.intersects(i.tempEnv2))return null;var l=Math.trunc((t+e)/2),d=Math.trunc((r+o)/2);t<l&&(r<d&&this.computeOverlapsInternal(t,l,n,r,d,i),d<o&&this.computeOverlapsInternal(t,l,n,d,o,i)),l<e&&(r<d&&this.computeOverlapsInternal(l,e,n,r,d,i),d<o&&this.computeOverlapsInternal(l,e,n,d,o,i))},cm.prototype.interfaces_=function(){return[]},cm.prototype.getClass=function(){return cm};var um=function(){};um.prototype.interfaces_=function(){return[]},um.prototype.getClass=function(){return um},um.getChainStartIndices=function(t){var e=0,n=new fh;n.add(new Ef(e));do{var r=um.findChainEnd(t,e);n.add(new Ef(r)),e=r}while(e<t.length-1);return um.toIntArray(n)},um.findChainEnd=function(t,e){for(var n=e;n<t.length-1&&t[n].equals2D(t[n+1]);)n++;if(n>=t.length-1)return t.length-1;for(var r=Mg.quadrant(t[n],t[n+1]),o=e+1;o<t.length;){if(!t[o-1].equals2D(t[o])){if(Mg.quadrant(t[o-1],t[o])!==r)break}o++}return o-1},um.getChains=function(){if(1===arguments.length){var t=arguments[0];return um.getChains(t,null)}if(2===arguments.length){for(var e=arguments[0],n=arguments[1],r=new fh,o=um.getChainStartIndices(e),i=0;i<o.length-1;i++){var a=new cm(e,o[i],o[i+1],n);r.add(a)}return r}},um.toIntArray=function(t){for(var e=new Array(t.size()).fill(null),n=0;n<e.length;n++)e[n]=t.get(n).intValue();return e};var lm=function(){};lm.prototype.computeNodes=function(t){},lm.prototype.getNodedSubstrings=function(){},lm.prototype.interfaces_=function(){return[]},lm.prototype.getClass=function(){return lm};var dm=function(){if(this._segInt=null,0===arguments.length);else if(1===arguments.length){var t=arguments[0];this.setSegmentIntersector(t)}};dm.prototype.setSegmentIntersector=function(t){this._segInt=t},dm.prototype.interfaces_=function(){return[lm]},dm.prototype.getClass=function(){return dm};var pm=function(t){function e(e){e?t.call(this,e):t.call(this),this._monoChains=new fh,this._index=new Kg,this._idCounter=0,this._nodedSegStrings=null,this._nOverlaps=0}t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e;var n={SegmentOverlapAction:{configurable:!0}};return e.prototype.getMonotoneChains=function(){return this._monoChains},e.prototype.getNodedSubstrings=function(){return om.getNodedSubstrings(this._nodedSegStrings)},e.prototype.getIndex=function(){return this._index},e.prototype.add=function(t){for(var e=this,n=um.getChains(t.getCoordinates(),t),r=n.iterator();r.hasNext();){var o=r.next();o.setId(e._idCounter++),e._index.insert(o.getEnvelope(),o),e._monoChains.add(o)}},e.prototype.computeNodes=function(t){var e=this;this._nodedSegStrings=t;for(var n=t.iterator();n.hasNext();)e.add(n.next());this.intersectChains()},e.prototype.intersectChains=function(){for(var t=this,e=new fm(this._segInt),n=this._monoChains.iterator();n.hasNext();)for(var r=n.next(),o=t._index.query(r.getEnvelope()),i=o.iterator();i.hasNext();){var a=i.next();if(a.getId()>r.getId()&&(r.computeOverlaps(a,e),t._nOverlaps++),t._segInt.isDone())return null}},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},n.SegmentOverlapAction.get=function(){return fm},Object.defineProperties(e,n),e}(dm),fm=function(t){function e(){t.call(this),this._si=null;var e=arguments[0];this._si=e}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.overlap=function(){if(4!==arguments.length)return t.prototype.overlap.apply(this,arguments);var e=arguments[0],n=arguments[1],r=arguments[2],o=arguments[3],i=e.getContext(),a=r.getContext();this._si.processIntersections(i,n,a,o)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(sm),hm=function t(){if(this._quadrantSegments=t.DEFAULT_QUADRANT_SEGMENTS,this._endCapStyle=t.CAP_ROUND,this._joinStyle=t.JOIN_ROUND,this._mitreLimit=t.DEFAULT_MITRE_LIMIT,this._isSingleSided=!1,this._simplifyFactor=t.DEFAULT_SIMPLIFY_FACTOR,0===arguments.length);else if(1===arguments.length){var e=arguments[0];this.setQuadrantSegments(e)}else if(2===arguments.length){var n=arguments[0],r=arguments[1];this.setQuadrantSegments(n),this.setEndCapStyle(r)}else if(4===arguments.length){var o=arguments[0],i=arguments[1],a=arguments[2],s=arguments[3];this.setQuadrantSegments(o),this.setEndCapStyle(i),this.setJoinStyle(a),this.setMitreLimit(s)}},gm={CAP_ROUND:{configurable:!0},CAP_FLAT:{configurable:!0},CAP_SQUARE:{configurable:!0},JOIN_ROUND:{configurable:!0},JOIN_MITRE:{configurable:!0},JOIN_BEVEL:{configurable:!0},DEFAULT_QUADRANT_SEGMENTS:{configurable:!0},DEFAULT_MITRE_LIMIT:{configurable:!0},DEFAULT_SIMPLIFY_FACTOR:{configurable:!0}};hm.prototype.getEndCapStyle=function(){return this._endCapStyle},hm.prototype.isSingleSided=function(){return this._isSingleSided},hm.prototype.setQuadrantSegments=function(t){this._quadrantSegments=t,0===this._quadrantSegments&&(this._joinStyle=hm.JOIN_BEVEL),this._quadrantSegments<0&&(this._joinStyle=hm.JOIN_MITRE,this._mitreLimit=Math.abs(this._quadrantSegments)),t<=0&&(this._quadrantSegments=1),this._joinStyle!==hm.JOIN_ROUND&&(this._quadrantSegments=hm.DEFAULT_QUADRANT_SEGMENTS)},hm.prototype.getJoinStyle=function(){return this._joinStyle},hm.prototype.setJoinStyle=function(t){this._joinStyle=t},hm.prototype.setSimplifyFactor=function(t){this._simplifyFactor=t<0?0:t},hm.prototype.getSimplifyFactor=function(){return this._simplifyFactor},hm.prototype.getQuadrantSegments=function(){return this._quadrantSegments},hm.prototype.setEndCapStyle=function(t){this._endCapStyle=t},hm.prototype.getMitreLimit=function(){return this._mitreLimit},hm.prototype.setMitreLimit=function(t){this._mitreLimit=t},hm.prototype.setSingleSided=function(t){this._isSingleSided=t},hm.prototype.interfaces_=function(){return[]},hm.prototype.getClass=function(){return hm},hm.bufferDistanceError=function(t){var e=Math.PI/2/t;return 1-Math.cos(e/2)},gm.CAP_ROUND.get=function(){return 1},gm.CAP_FLAT.get=function(){return 2},gm.CAP_SQUARE.get=function(){return 3},gm.JOIN_ROUND.get=function(){return 1},gm.JOIN_MITRE.get=function(){return 2},gm.JOIN_BEVEL.get=function(){return 3},gm.DEFAULT_QUADRANT_SEGMENTS.get=function(){return 8},gm.DEFAULT_MITRE_LIMIT.get=function(){return 5},gm.DEFAULT_SIMPLIFY_FACTOR.get=function(){return.01},Object.defineProperties(hm,gm);var mm=function(t){this._distanceTol=null,this._isDeleted=null,this._angleOrientation=Kf.COUNTERCLOCKWISE,this._inputLine=t||null},ym={INIT:{configurable:!0},DELETE:{configurable:!0},KEEP:{configurable:!0},NUM_PTS_TO_CHECK:{configurable:!0}};mm.prototype.isDeletable=function(t,e,n,r){var o=this._inputLine[t],i=this._inputLine[e],a=this._inputLine[n];return!!this.isConcave(o,i,a)&&(!!this.isShallow(o,i,a,r)&&this.isShallowSampled(o,i,t,n,r))},mm.prototype.deleteShallowConcavities=function(){for(var t=this,e=1,n=this.findNextNonDeletedIndex(e),r=this.findNextNonDeletedIndex(n),o=!1;r<this._inputLine.length;){var i=!1;t.isDeletable(e,n,r,t._distanceTol)&&(t._isDeleted[n]=mm.DELETE,i=!0,o=!0),e=i?r:n,n=t.findNextNonDeletedIndex(e),r=t.findNextNonDeletedIndex(n)}return o},mm.prototype.isShallowConcavity=function(t,e,n,r){return Kf.computeOrientation(t,e,n)===this._angleOrientation&&Kf.distancePointLine(e,t,n)<r},mm.prototype.isShallowSampled=function(t,e,n,r,o){var i=this,a=Math.trunc((r-n)/mm.NUM_PTS_TO_CHECK);a<=0&&(a=1);for(var s=n;s<r;s+=a)if(!i.isShallow(t,e,i._inputLine[s],o))return!1;return!0},
mm.prototype.isConcave=function(t,e,n){return Kf.computeOrientation(t,e,n)===this._angleOrientation},mm.prototype.simplify=function(t){var e=this;this._distanceTol=Math.abs(t),t<0&&(this._angleOrientation=Kf.CLOCKWISE),this._isDeleted=new Array(this._inputLine.length).fill(null);var n=!1;do{n=e.deleteShallowConcavities()}while(n);return this.collapseLine()},mm.prototype.findNextNonDeletedIndex=function(t){for(var e=t+1;e<this._inputLine.length&&this._isDeleted[e]===mm.DELETE;)e++;return e},mm.prototype.isShallow=function(t,e,n,r){return Kf.distancePointLine(e,t,n)<r},mm.prototype.collapseLine=function(){for(var t=this,e=new gh,n=0;n<this._inputLine.length;n++)t._isDeleted[n]!==mm.DELETE&&e.add(t._inputLine[n]);return e.toCoordinateArray()},mm.prototype.interfaces_=function(){return[]},mm.prototype.getClass=function(){return mm},mm.simplify=function(t,e){return new mm(t).simplify(e)},ym.INIT.get=function(){return 0},ym.DELETE.get=function(){return 1},ym.KEEP.get=function(){return 1},ym.NUM_PTS_TO_CHECK.get=function(){return 10},Object.defineProperties(mm,ym);var vm=function(){this._ptList=null,this._precisionModel=null,this._minimimVertexDistance=0,this._ptList=new fh},_m={COORDINATE_ARRAY_TYPE:{configurable:!0}};vm.prototype.getCoordinates=function(){return this._ptList.toArray(vm.COORDINATE_ARRAY_TYPE)},vm.prototype.setPrecisionModel=function(t){this._precisionModel=t},vm.prototype.addPt=function(t){var e=new ff(t);if(this._precisionModel.makePrecise(e),this.isRedundant(e))return null;this._ptList.add(e)},vm.prototype.revere=function(){},vm.prototype.addPts=function(t,e){var n=this;if(e)for(var r=0;r<t.length;r++)n.addPt(t[r]);else for(var o=t.length-1;o>=0;o--)n.addPt(t[o])},vm.prototype.isRedundant=function(t){if(this._ptList.size()<1)return!1;var e=this._ptList.get(this._ptList.size()-1);return t.distance(e)<this._minimimVertexDistance},vm.prototype.toString=function(){return(new cg).createLineString(this.getCoordinates()).toString()},vm.prototype.closeRing=function(){if(this._ptList.size()<1)return null;var t=new ff(this._ptList.get(0)),e=this._ptList.get(this._ptList.size()-1);if(t.equals(e))return null;this._ptList.add(t)},vm.prototype.setMinimumVertexDistance=function(t){this._minimimVertexDistance=t},vm.prototype.interfaces_=function(){return[]},vm.prototype.getClass=function(){return vm},_m.COORDINATE_ARRAY_TYPE.get=function(){return new Array(0).fill(null)},Object.defineProperties(vm,_m);var bm=function(){},wm={PI_TIMES_2:{configurable:!0},PI_OVER_2:{configurable:!0},PI_OVER_4:{configurable:!0},COUNTERCLOCKWISE:{configurable:!0},CLOCKWISE:{configurable:!0},NONE:{configurable:!0}};bm.prototype.interfaces_=function(){return[]},bm.prototype.getClass=function(){return bm},bm.toDegrees=function(t){return 180*t/Math.PI},bm.normalize=function(t){for(;t>Math.PI;)t-=bm.PI_TIMES_2;for(;t<=-Math.PI;)t+=bm.PI_TIMES_2;return t},bm.angle=function(){if(1===arguments.length){var t=arguments[0];return Math.atan2(t.y,t.x)}if(2===arguments.length){var e=arguments[0],n=arguments[1],r=n.x-e.x,o=n.y-e.y;return Math.atan2(o,r)}},bm.isAcute=function(t,e,n){var r=t.x-e.x,o=t.y-e.y;return r*(n.x-e.x)+o*(n.y-e.y)>0},bm.isObtuse=function(t,e,n){var r=t.x-e.x,o=t.y-e.y;return r*(n.x-e.x)+o*(n.y-e.y)<0},bm.interiorAngle=function(t,e,n){var r=bm.angle(e,t),o=bm.angle(e,n);return Math.abs(o-r)},bm.normalizePositive=function(t){if(t<0){for(;t<0;)t+=bm.PI_TIMES_2;t>=bm.PI_TIMES_2&&(t=0)}else{for(;t>=bm.PI_TIMES_2;)t-=bm.PI_TIMES_2;t<0&&(t=0)}return t},bm.angleBetween=function(t,e,n){var r=bm.angle(e,t),o=bm.angle(e,n);return bm.diff(r,o)},bm.diff=function(t,e){var n=null;return n=t<e?e-t:t-e,n>Math.PI&&(n=2*Math.PI-n),n},bm.toRadians=function(t){return t*Math.PI/180},bm.getTurn=function(t,e){var n=Math.sin(e-t);return n>0?bm.COUNTERCLOCKWISE:n<0?bm.CLOCKWISE:bm.NONE},bm.angleBetweenOriented=function(t,e,n){var r=bm.angle(e,t),o=bm.angle(e,n),i=o-r;return i<=-Math.PI?i+bm.PI_TIMES_2:i>Math.PI?i-bm.PI_TIMES_2:i},wm.PI_TIMES_2.get=function(){return 2*Math.PI},wm.PI_OVER_2.get=function(){return Math.PI/2},wm.PI_OVER_4.get=function(){return Math.PI/4},wm.COUNTERCLOCKWISE.get=function(){return Kf.COUNTERCLOCKWISE},wm.CLOCKWISE.get=function(){return Kf.CLOCKWISE},wm.NONE.get=function(){return Kf.COLLINEAR},Object.defineProperties(bm,wm);var xm=function t(){this._maxCurveSegmentError=0,this._filletAngleQuantum=null,this._closingSegLengthFactor=1,this._segList=null,this._distance=0,this._precisionModel=null,this._bufParams=null,this._li=null,this._s0=null,this._s1=null,this._s2=null,this._seg0=new im,this._seg1=new im,this._offset0=new im,this._offset1=new im,this._side=0,this._hasNarrowConcaveAngle=!1;var e=arguments[0],n=arguments[1],r=arguments[2];this._precisionModel=e,this._bufParams=n,this._li=new Wf,this._filletAngleQuantum=Math.PI/2/n.getQuadrantSegments(),n.getQuadrantSegments()>=8&&n.getJoinStyle()===hm.JOIN_ROUND&&(this._closingSegLengthFactor=t.MAX_CLOSING_SEG_LEN_FACTOR),this.init(r)},Em={OFFSET_SEGMENT_SEPARATION_FACTOR:{configurable:!0},INSIDE_TURN_VERTEX_SNAP_DISTANCE_FACTOR:{configurable:!0},CURVE_VERTEX_SNAP_DISTANCE_FACTOR:{configurable:!0},MAX_CLOSING_SEG_LEN_FACTOR:{configurable:!0}};xm.prototype.addNextSegment=function(t,e){if(this._s0=this._s1,this._s1=this._s2,this._s2=t,this._seg0.setCoordinates(this._s0,this._s1),this.computeOffsetSegment(this._seg0,this._side,this._distance,this._offset0),this._seg1.setCoordinates(this._s1,this._s2),this.computeOffsetSegment(this._seg1,this._side,this._distance,this._offset1),this._s1.equals(this._s2))return null;var n=Kf.computeOrientation(this._s0,this._s1,this._s2),r=n===Kf.CLOCKWISE&&this._side===mg.LEFT||n===Kf.COUNTERCLOCKWISE&&this._side===mg.RIGHT;0===n?this.addCollinear(e):r?this.addOutsideTurn(n,e):this.addInsideTurn(n,e)},xm.prototype.addLineEndCap=function(t,e){var n=new im(t,e),r=new im;this.computeOffsetSegment(n,mg.LEFT,this._distance,r);var o=new im;this.computeOffsetSegment(n,mg.RIGHT,this._distance,o);var i=e.x-t.x,a=e.y-t.y,s=Math.atan2(a,i);switch(this._bufParams.getEndCapStyle()){case hm.CAP_ROUND:this._segList.addPt(r.p1),this.addFilletArc(e,s+Math.PI/2,s-Math.PI/2,Kf.CLOCKWISE,this._distance),this._segList.addPt(o.p1);break;case hm.CAP_FLAT:this._segList.addPt(r.p1),this._segList.addPt(o.p1);break;case hm.CAP_SQUARE:var c=new ff;c.x=Math.abs(this._distance)*Math.cos(s),c.y=Math.abs(this._distance)*Math.sin(s);var u=new ff(r.p1.x+c.x,r.p1.y+c.y),l=new ff(o.p1.x+c.x,o.p1.y+c.y);this._segList.addPt(u),this._segList.addPt(l)}},xm.prototype.getCoordinates=function(){return this._segList.getCoordinates()},xm.prototype.addMitreJoin=function(t,e,n,r){var o=!0,i=null;try{i=Tf.intersection(e.p0,e.p1,n.p0,n.p1);(r<=0?1:i.distance(t)/Math.abs(r))>this._bufParams.getMitreLimit()&&(o=!1)}catch(t){if(!(t instanceof Df))throw t;i=new ff(0,0),o=!1}o?this._segList.addPt(i):this.addLimitedMitreJoin(e,n,r,this._bufParams.getMitreLimit())},xm.prototype.addFilletCorner=function(t,e,n,r,o){var i=e.x-t.x,a=e.y-t.y,s=Math.atan2(a,i),c=n.x-t.x,u=n.y-t.y,l=Math.atan2(u,c);r===Kf.CLOCKWISE?s<=l&&(s+=2*Math.PI):s>=l&&(s-=2*Math.PI),this._segList.addPt(e),this.addFilletArc(t,s,l,r,o),this._segList.addPt(n)},xm.prototype.addOutsideTurn=function(t,e){if(this._offset0.p1.distance(this._offset1.p0)<this._distance*xm.OFFSET_SEGMENT_SEPARATION_FACTOR)return this._segList.addPt(this._offset0.p1),null;this._bufParams.getJoinStyle()===hm.JOIN_MITRE?this.addMitreJoin(this._s1,this._offset0,this._offset1,this._distance):this._bufParams.getJoinStyle()===hm.JOIN_BEVEL?this.addBevelJoin(this._offset0,this._offset1):(e&&this._segList.addPt(this._offset0.p1),this.addFilletCorner(this._s1,this._offset0.p1,this._offset1.p0,t,this._distance),this._segList.addPt(this._offset1.p0))},xm.prototype.createSquare=function(t){this._segList.addPt(new ff(t.x+this._distance,t.y+this._distance)),this._segList.addPt(new ff(t.x+this._distance,t.y-this._distance)),this._segList.addPt(new ff(t.x-this._distance,t.y-this._distance)),this._segList.addPt(new ff(t.x-this._distance,t.y+this._distance)),this._segList.closeRing()},xm.prototype.addSegments=function(t,e){this._segList.addPts(t,e)},xm.prototype.addFirstSegment=function(){this._segList.addPt(this._offset1.p0)},xm.prototype.addLastSegment=function(){this._segList.addPt(this._offset1.p1)},xm.prototype.initSideSegments=function(t,e,n){this._s1=t,this._s2=e,this._side=n,this._seg1.setCoordinates(t,e),this.computeOffsetSegment(this._seg1,n,this._distance,this._offset1)},xm.prototype.addLimitedMitreJoin=function(t,e,n,r){var o=this._seg0.p1,i=bm.angle(o,this._seg0.p0),a=bm.angleBetweenOriented(this._seg0.p0,o,this._seg1.p1),s=a/2,c=bm.normalize(i+s),u=bm.normalize(c+Math.PI),l=r*n,d=l*Math.abs(Math.sin(s)),p=n-d,f=o.x+l*Math.cos(u),h=o.y+l*Math.sin(u),g=new ff(f,h),m=new im(o,g),y=m.pointAlongOffset(1,p),v=m.pointAlongOffset(1,-p);this._side===mg.LEFT?(this._segList.addPt(y),this._segList.addPt(v)):(this._segList.addPt(v),this._segList.addPt(y))},xm.prototype.computeOffsetSegment=function(t,e,n,r){var o=e===mg.LEFT?1:-1,i=t.p1.x-t.p0.x,a=t.p1.y-t.p0.y,s=Math.sqrt(i*i+a*a),c=o*n*i/s,u=o*n*a/s;r.p0.x=t.p0.x-u,r.p0.y=t.p0.y+c,r.p1.x=t.p1.x-u,r.p1.y=t.p1.y+c},xm.prototype.addFilletArc=function(t,e,n,r,o){var i=this,a=r===Kf.CLOCKWISE?-1:1,s=Math.abs(e-n),c=Math.trunc(s/this._filletAngleQuantum+.5);if(c<1)return null;for(var u=s/c,l=0,d=new ff;l<s;){var p=e+a*l;d.x=t.x+o*Math.cos(p),d.y=t.y+o*Math.sin(p),i._segList.addPt(d),l+=u}},xm.prototype.addInsideTurn=function(t,e){if(this._li.computeIntersection(this._offset0.p0,this._offset0.p1,this._offset1.p0,this._offset1.p1),this._li.hasIntersection())this._segList.addPt(this._li.getIntersection(0));else if(this._hasNarrowConcaveAngle=!0,this._offset0.p1.distance(this._offset1.p0)<this._distance*xm.INSIDE_TURN_VERTEX_SNAP_DISTANCE_FACTOR)this._segList.addPt(this._offset0.p1);else{if(this._segList.addPt(this._offset0.p1),this._closingSegLengthFactor>0){var n=new ff((this._closingSegLengthFactor*this._offset0.p1.x+this._s1.x)/(this._closingSegLengthFactor+1),(this._closingSegLengthFactor*this._offset0.p1.y+this._s1.y)/(this._closingSegLengthFactor+1));this._segList.addPt(n);var r=new ff((this._closingSegLengthFactor*this._offset1.p0.x+this._s1.x)/(this._closingSegLengthFactor+1),(this._closingSegLengthFactor*this._offset1.p0.y+this._s1.y)/(this._closingSegLengthFactor+1));this._segList.addPt(r)}else this._segList.addPt(this._s1);this._segList.addPt(this._offset1.p0)}},xm.prototype.createCircle=function(t){var e=new ff(t.x+this._distance,t.y);this._segList.addPt(e),this.addFilletArc(t,0,2*Math.PI,-1,this._distance),this._segList.closeRing()},xm.prototype.addBevelJoin=function(t,e){this._segList.addPt(t.p1),this._segList.addPt(e.p0)},xm.prototype.init=function(t){this._distance=t,this._maxCurveSegmentError=t*(1-Math.cos(this._filletAngleQuantum/2)),this._segList=new vm,this._segList.setPrecisionModel(this._precisionModel),this._segList.setMinimumVertexDistance(t*xm.CURVE_VERTEX_SNAP_DISTANCE_FACTOR)},xm.prototype.addCollinear=function(t){this._li.computeIntersection(this._s0,this._s1,this._s1,this._s2),this._li.getIntersectionNum()>=2&&(this._bufParams.getJoinStyle()===hm.JOIN_BEVEL||this._bufParams.getJoinStyle()===hm.JOIN_MITRE?(t&&this._segList.addPt(this._offset0.p1),this._segList.addPt(this._offset1.p0)):this.addFilletCorner(this._s1,this._offset0.p1,this._offset1.p0,Kf.CLOCKWISE,this._distance))},xm.prototype.closeRing=function(){this._segList.closeRing()},xm.prototype.hasNarrowConcaveAngle=function(){return this._hasNarrowConcaveAngle},xm.prototype.interfaces_=function(){return[]},xm.prototype.getClass=function(){return xm},Em.OFFSET_SEGMENT_SEPARATION_FACTOR.get=function(){return.001},Em.INSIDE_TURN_VERTEX_SNAP_DISTANCE_FACTOR.get=function(){return.001},Em.CURVE_VERTEX_SNAP_DISTANCE_FACTOR.get=function(){return 1e-6},Em.MAX_CLOSING_SEG_LEN_FACTOR.get=function(){return 80},Object.defineProperties(xm,Em);var Cm=function(){this._distance=0,this._precisionModel=null,this._bufParams=null;var t=arguments[0],e=arguments[1];this._precisionModel=t,this._bufParams=e};Cm.prototype.getOffsetCurve=function(t,e){if(this._distance=e,0===e)return null;var n=e<0,r=Math.abs(e),o=this.getSegGen(r);t.length<=1?this.computePointCurve(t[0],o):this.computeOffsetCurve(t,n,o);var i=o.getCoordinates();return n&&mh.reverse(i),i},Cm.prototype.computeSingleSidedBufferCurve=function(t,e,n){var r=this.simplifyTolerance(this._distance);if(e){n.addSegments(t,!0);var o=mm.simplify(t,-r),i=o.length-1;n.initSideSegments(o[i],o[i-1],mg.LEFT),n.addFirstSegment();for(var a=i-2;a>=0;a--)n.addNextSegment(o[a],!0)}else{n.addSegments(t,!1);var s=mm.simplify(t,r),c=s.length-1;n.initSideSegments(s[0],s[1],mg.LEFT),n.addFirstSegment();for(var u=2;u<=c;u++)n.addNextSegment(s[u],!0)}n.addLastSegment(),n.closeRing()},Cm.prototype.computeRingBufferCurve=function(t,e,n){var r=this.simplifyTolerance(this._distance);e===mg.RIGHT&&(r=-r);var o=mm.simplify(t,r),i=o.length-1;n.initSideSegments(o[i-1],o[0],e);for(var a=1;a<=i;a++){var s=1!==a;n.addNextSegment(o[a],s)}n.closeRing()},Cm.prototype.computeLineBufferCurve=function(t,e){var n=this.simplifyTolerance(this._distance),r=mm.simplify(t,n),o=r.length-1;e.initSideSegments(r[0],r[1],mg.LEFT);for(var i=2;i<=o;i++)e.addNextSegment(r[i],!0);e.addLastSegment(),e.addLineEndCap(r[o-1],r[o]);var a=mm.simplify(t,-n),s=a.length-1;e.initSideSegments(a[s],a[s-1],mg.LEFT);for(var c=s-2;c>=0;c--)e.addNextSegment(a[c],!0);e.addLastSegment(),e.addLineEndCap(a[1],a[0]),e.closeRing()},Cm.prototype.computePointCurve=function(t,e){switch(this._bufParams.getEndCapStyle()){case hm.CAP_ROUND:e.createCircle(t);break;case hm.CAP_SQUARE:e.createSquare(t)}},Cm.prototype.getLineCurve=function(t,e){if(this._distance=e,e<0&&!this._bufParams.isSingleSided())return null;if(0===e)return null;var n=Math.abs(e),r=this.getSegGen(n);if(t.length<=1)this.computePointCurve(t[0],r);else if(this._bufParams.isSingleSided()){var o=e<0;this.computeSingleSidedBufferCurve(t,o,r)}else this.computeLineBufferCurve(t,r);return r.getCoordinates()},Cm.prototype.getBufferParameters=function(){return this._bufParams},Cm.prototype.simplifyTolerance=function(t){return t*this._bufParams.getSimplifyFactor()},Cm.prototype.getRingCurve=function(t,e,n){if(this._distance=n,t.length<=2)return this.getLineCurve(t,n);if(0===n)return Cm.copyCoordinates(t);var r=this.getSegGen(n);return this.computeRingBufferCurve(t,e,r),r.getCoordinates()},Cm.prototype.computeOffsetCurve=function(t,e,n){var r=this.simplifyTolerance(this._distance);if(e){var o=mm.simplify(t,-r),i=o.length-1;n.initSideSegments(o[i],o[i-1],mg.LEFT),n.addFirstSegment();for(var a=i-2;a>=0;a--)n.addNextSegment(o[a],!0)}else{var s=mm.simplify(t,r),c=s.length-1;n.initSideSegments(s[0],s[1],mg.LEFT),n.addFirstSegment();for(var u=2;u<=c;u++)n.addNextSegment(s[u],!0)}n.addLastSegment()},Cm.prototype.getSegGen=function(t){return new xm(this._precisionModel,this._bufParams,t)},Cm.prototype.interfaces_=function(){return[]},Cm.prototype.getClass=function(){return Cm},Cm.copyCoordinates=function(t){for(var e=new Array(t.length).fill(null),n=0;n<e.length;n++)e[n]=new ff(t[n]);return e};var Sm=function(){this._subgraphs=null,this._seg=new im,this._cga=new Kf;var t=arguments[0];this._subgraphs=t},Im={DepthSegment:{configurable:!0}};Sm.prototype.findStabbedSegments=function(){var t=this;if(1===arguments.length){for(var e=arguments[0],n=new fh,r=this._subgraphs.iterator();r.hasNext();){var o=r.next(),i=o.getEnvelope();e.y<i.getMinY()||e.y>i.getMaxY()||t.findStabbedSegments(e,o.getDirectedEdges(),n)}return n}if(3===arguments.length)if(_f(arguments[2],ph)&&arguments[0]instanceof ff&&arguments[1]instanceof Og)for(var a=arguments[0],s=arguments[1],c=arguments[2],u=s.getEdge().getCoordinates(),l=0;l<u.length-1;l++){t._seg.p0=u[l],t._seg.p1=u[l+1],t._seg.p0.y>t._seg.p1.y&&t._seg.reverse();var d=Math.max(t._seg.p0.x,t._seg.p1.x);if(!(d<a.x)&&!(t._seg.isHorizontal()||a.y<t._seg.p0.y||a.y>t._seg.p1.y||Kf.computeOrientation(t._seg.p0,t._seg.p1,a)===Kf.RIGHT)){var p=s.getDepth(mg.LEFT);t._seg.p0.equals(u[l])||(p=s.getDepth(mg.RIGHT));var f=new Pm(t._seg,p);c.add(f)}}else if(_f(arguments[2],ph)&&arguments[0]instanceof ff&&_f(arguments[1],ph))for(var h=arguments[0],g=arguments[1],m=arguments[2],y=g.iterator();y.hasNext();){var v=y.next();v.isForward()&&t.findStabbedSegments(h,v,m)}},Sm.prototype.getDepth=function(t){var e=this.findStabbedSegments(t);return 0===e.size()?0:jg.min(e)._leftDepth},Sm.prototype.interfaces_=function(){return[]},Sm.prototype.getClass=function(){return Sm},Im.DepthSegment.get=function(){return Pm},Object.defineProperties(Sm,Im);var Pm=function(){this._upwardSeg=null,this._leftDepth=null;var t=arguments[0],e=arguments[1];this._upwardSeg=new im(t),this._leftDepth=e};Pm.prototype.compareTo=function(t){var e=t;if(this._upwardSeg.minX()>=e._upwardSeg.maxX())return 1;if(this._upwardSeg.maxX()<=e._upwardSeg.minX())return-1;var n=this._upwardSeg.orientationIndex(e._upwardSeg);return 0!==n?n:(n=-1*e._upwardSeg.orientationIndex(this._upwardSeg),0!==n?n:this._upwardSeg.compareTo(e._upwardSeg))},Pm.prototype.compareX=function(t,e){var n=t.p0.compareTo(e.p0);return 0!==n?n:t.p1.compareTo(e.p1)},Pm.prototype.toString=function(){return this._upwardSeg.toString()},Pm.prototype.interfaces_=function(){return[lf]},Pm.prototype.getClass=function(){return Pm};var Am=function(t,e,n){this.p0=t||null,this.p1=e||null,this.p2=n||null};Am.prototype.area=function(){return Am.area(this.p0,this.p1,this.p2)},Am.prototype.signedArea=function(){return Am.signedArea(this.p0,this.p1,this.p2)},Am.prototype.interpolateZ=function(t){if(null===t)throw new sf("Supplied point is null.");return Am.interpolateZ(t,this.p0,this.p1,this.p2)},Am.prototype.longestSideLength=function(){return Am.longestSideLength(this.p0,this.p1,this.p2)},Am.prototype.isAcute=function(){return Am.isAcute(this.p0,this.p1,this.p2)},Am.prototype.circumcentre=function(){return Am.circumcentre(this.p0,this.p1,this.p2)},Am.prototype.area3D=function(){return Am.area3D(this.p0,this.p1,this.p2)},Am.prototype.centroid=function(){return Am.centroid(this.p0,this.p1,this.p2)},Am.prototype.inCentre=function(){return Am.inCentre(this.p0,this.p1,this.p2)},Am.prototype.interfaces_=function(){return[]},Am.prototype.getClass=function(){return Am},Am.area=function(t,e,n){return Math.abs(((n.x-t.x)*(e.y-t.y)-(e.x-t.x)*(n.y-t.y))/2)},Am.signedArea=function(t,e,n){return((n.x-t.x)*(e.y-t.y)-(e.x-t.x)*(n.y-t.y))/2},Am.det=function(t,e,n,r){return t*r-e*n},Am.interpolateZ=function(t,e,n,r){var o=e.x,i=e.y,a=n.x-o,s=r.x-o,c=n.y-i,u=r.y-i,l=a*u-s*c,d=t.x-o,p=t.y-i,f=(u*d-s*p)/l,h=(-c*d+a*p)/l;return e.z+f*(n.z-e.z)+h*(r.z-e.z)},Am.longestSideLength=function(t,e,n){var r=t.distance(e),o=e.distance(n),i=n.distance(t),a=r;return o>a&&(a=o),i>a&&(a=i),a},Am.isAcute=function(t,e,n){return!!bm.isAcute(t,e,n)&&(!!bm.isAcute(e,n,t)&&!!bm.isAcute(n,t,e))},Am.circumcentre=function(t,e,n){var r=n.x,o=n.y,i=t.x-r,a=t.y-o,s=e.x-r,c=e.y-o,u=2*Am.det(i,a,s,c),l=Am.det(a,i*i+a*a,c,s*s+c*c),d=Am.det(i,i*i+a*a,s,s*s+c*c);return new ff(r-l/u,o+d/u)},Am.perpendicularBisector=function(t,e){var n=e.x-t.x,r=e.y-t.y,o=new Tf(t.x+n/2,t.y+r/2,1),i=new Tf(t.x-r+n/2,t.y+n+r/2,1);return new Tf(o,i)},Am.angleBisector=function(t,e,n){var r=e.distance(t),o=e.distance(n),i=r/(r+o),a=n.x-t.x,s=n.y-t.y;return new ff(t.x+i*a,t.y+i*s)},Am.area3D=function(t,e,n){var r=e.x-t.x,o=e.y-t.y,i=e.z-t.z,a=n.x-t.x,s=n.y-t.y,c=n.z-t.z,u=o*c-i*s,l=i*a-r*c,d=r*s-o*a,p=u*u+l*l+d*d;return Math.sqrt(p)/2},Am.centroid=function(t,e,n){var r=(t.x+e.x+n.x)/3,o=(t.y+e.y+n.y)/3;return new ff(r,o)},Am.inCentre=function(t,e,n){var r=e.distance(n),o=t.distance(n),i=t.distance(e),a=r+o+i,s=(r*t.x+o*e.x+i*n.x)/a,c=(r*t.y+o*e.y+i*n.y)/a;return new ff(s,c)};var Nm=function(){this._inputGeom=null,this._distance=null,this._curveBuilder=null,this._curveList=new fh;var t=arguments[0],e=arguments[1],n=arguments[2];this._inputGeom=t,this._distance=e,this._curveBuilder=n};Nm.prototype.addPoint=function(t){if(this._distance<=0)return null;var e=t.getCoordinates(),n=this._curveBuilder.getLineCurve(e,this._distance);this.addCurve(n,yf.EXTERIOR,yf.INTERIOR)},Nm.prototype.addPolygon=function(t){var e=this,n=this._distance,r=mg.LEFT;this._distance<0&&(n=-this._distance,r=mg.RIGHT);var o=t.getExteriorRing(),i=mh.removeRepeatedPoints(o.getCoordinates());if(this._distance<0&&this.isErodedCompletely(o,this._distance))return null;if(this._distance<=0&&i.length<3)return null;this.addPolygonRing(i,n,r,yf.EXTERIOR,yf.INTERIOR);for(var a=0;a<t.getNumInteriorRing();a++){var s=t.getInteriorRingN(a),c=mh.removeRepeatedPoints(s.getCoordinates());e._distance>0&&e.isErodedCompletely(s,-e._distance)||e.addPolygonRing(c,n,mg.opposite(r),yf.INTERIOR,yf.EXTERIOR)}},Nm.prototype.isTriangleErodedCompletely=function(t,e){var n=new Am(t[0],t[1],t[2]),r=n.inCentre();return Kf.distancePointLine(r,n.p0,n.p1)<Math.abs(e)},Nm.prototype.addLineString=function(t){if(this._distance<=0&&!this._curveBuilder.getBufferParameters().isSingleSided())return null;var e=mh.removeRepeatedPoints(t.getCoordinates()),n=this._curveBuilder.getLineCurve(e,this._distance);this.addCurve(n,yf.EXTERIOR,yf.INTERIOR)},Nm.prototype.addCurve=function(t,e,n){if(null===t||t.length<2)return null;var r=new om(t,new Eg(0,yf.BOUNDARY,e,n));this._curveList.add(r)},Nm.prototype.getCurves=function(){return this.add(this._inputGeom),this._curveList},Nm.prototype.addPolygonRing=function(t,e,n,r,o){if(0===e&&t.length<Xh.MINIMUM_VALID_SIZE)return null;var i=r,a=o;t.length>=Xh.MINIMUM_VALID_SIZE&&Kf.isCCW(t)&&(i=o,a=r,n=mg.opposite(n));var s=this._curveBuilder.getRingCurve(t,n,e);this.addCurve(s,i,a)},Nm.prototype.add=function(t){if(t.isEmpty())return null;t instanceof jh?this.addPolygon(t):t instanceof Uh?this.addLineString(t):t instanceof Vh?this.addPoint(t):t instanceof Yh?this.addCollection(t):t instanceof Oh?this.addCollection(t):t instanceof Wh?this.addCollection(t):t instanceof Dh&&this.addCollection(t)},Nm.prototype.isErodedCompletely=function(t,e){var n=t.getCoordinates();if(n.length<4)return e<0;if(4===n.length)return this.isTriangleErodedCompletely(n,e);var r=t.getEnvelopeInternal(),o=Math.min(r.getHeight(),r.getWidth());return e<0&&2*Math.abs(e)>o},Nm.prototype.addCollection=function(t){for(var e=this,n=0;n<t.getNumGeometries();n++){var r=t.getGeometryN(n);e.add(r)}},Nm.prototype.interfaces_=function(){return[]},Nm.prototype.getClass=function(){return Nm};var Mm=function(){};Mm.prototype.locate=function(t){},Mm.prototype.interfaces_=function(){return[]},Mm.prototype.getClass=function(){return Mm};var Lm=function(){this._parent=null,this._atStart=null,this._max=null,this._index=null,this._subcollectionIterator=null;var t=arguments[0];this._parent=t,this._atStart=!0,this._index=0,this._max=t.getNumGeometries()};Lm.prototype.next=function(){if(this._atStart)return this._atStart=!1,Lm.isAtomic(this._parent)&&this._index++,this._parent;if(null!==this._subcollectionIterator){if(this._subcollectionIterator.hasNext())return this._subcollectionIterator.next();this._subcollectionIterator=null}if(this._index>=this._max)throw new ac;var t=this._parent.getGeometryN(this._index++);return t instanceof Dh?(this._subcollectionIterator=new Lm(t),this._subcollectionIterator.next()):t},Lm.prototype.remove=function(){throw new Error(this.getClass().getName())},Lm.prototype.hasNext=function(){if(this._atStart)return!0;if(null!==this._subcollectionIterator){if(this._subcollectionIterator.hasNext())return!0;this._subcollectionIterator=null}return!(this._index>=this._max)},Lm.prototype.interfaces_=function(){return[dh]},Lm.prototype.getClass=function(){return Lm},Lm.isAtomic=function(t){return!(t instanceof Dh)};var Dm=function(){this._geom=null;var t=arguments[0];this._geom=t};Dm.prototype.locate=function(t){return Dm.locate(t,this._geom)},Dm.prototype.interfaces_=function(){return[Mm]},Dm.prototype.getClass=function(){return Dm},Dm.isPointInRing=function(t,e){return!!e.getEnvelopeInternal().intersects(t)&&Kf.isPointInRing(t,e.getCoordinates())},Dm.containsPointInPolygon=function(t,e){if(e.isEmpty())return!1;var n=e.getExteriorRing();if(!Dm.isPointInRing(t,n))return!1;for(var r=0;r<e.getNumInteriorRing();r++){var o=e.getInteriorRingN(r);if(Dm.isPointInRing(t,o))return!1}return!0},Dm.containsPoint=function(t,e){if(e instanceof jh)return Dm.containsPointInPolygon(t,e);if(e instanceof Dh)for(var n=new Lm(e);n.hasNext();){var r=n.next();if(r!==e&&Dm.containsPoint(t,r))return!0}return!1},Dm.locate=function(t,e){return e.isEmpty()?yf.EXTERIOR:Dm.containsPoint(t,e)?yf.INTERIOR:yf.EXTERIOR};var Om=function(){this._edgeMap=new hc,this._edgeList=null,this._ptInAreaLocation=[yf.NONE,yf.NONE]};Om.prototype.getNextCW=function(t){this.getEdges();var e=this._edgeList.indexOf(t),n=e-1;return 0===e&&(n=this._edgeList.size()-1),this._edgeList.get(n)},Om.prototype.propagateSideLabels=function(t){for(var e=yf.NONE,n=this.iterator();n.hasNext();){var r=n.next(),o=r.getLabel();o.isArea(t)&&o.getLocation(t,mg.LEFT)!==yf.NONE&&(e=o.getLocation(t,mg.LEFT))}if(e===yf.NONE)return null;for(var i=e,a=this.iterator();a.hasNext();){var s=a.next(),c=s.getLabel();if(c.getLocation(t,mg.ON)===yf.NONE&&c.setLocation(t,mg.ON,i),c.isArea(t)){var u=c.getLocation(t,mg.LEFT),l=c.getLocation(t,mg.RIGHT);if(l!==yf.NONE){if(l!==i)throw new _g("side location conflict",s.getCoordinate());u===yf.NONE&&jf.shouldNeverReachHere("found single null side (at "+s.getCoordinate()+")"),i=u}else jf.isTrue(c.getLocation(t,mg.LEFT)===yf.NONE,"found single null side"),c.setLocation(t,mg.RIGHT,i),c.setLocation(t,mg.LEFT,i)}}},Om.prototype.getCoordinate=function(){var t=this.iterator();return t.hasNext()?t.next().getCoordinate():null},Om.prototype.print=function(t){Of.out.println("EdgeEndStar:   "+this.getCoordinate());for(var e=this.iterator();e.hasNext();){e.next().print(t)}},Om.prototype.isAreaLabelsConsistent=function(t){return this.computeEdgeEndLabels(t.getBoundaryNodeRule()),this.checkAreaLabelsConsistent(0)},Om.prototype.checkAreaLabelsConsistent=function(t){var e=this.getEdges();if(e.size()<=0)return!0;var n=e.size()-1,r=e.get(n).getLabel(),o=r.getLocation(t,mg.LEFT);jf.isTrue(o!==yf.NONE,"Found unlabelled area edge");for(var i=o,a=this.iterator();a.hasNext();){var s=a.next(),c=s.getLabel();jf.isTrue(c.isArea(t),"Found non-area edge");var u=c.getLocation(t,mg.LEFT),l=c.getLocation(t,mg.RIGHT);if(u===l)return!1;if(l!==i)return!1;i=u}return!0},Om.prototype.findIndex=function(t){var e=this;this.iterator();for(var n=0;n<this._edgeList.size();n++){if(e._edgeList.get(n)===t)return n}return-1},Om.prototype.iterator=function(){return this.getEdges().iterator()},Om.prototype.getEdges=function(){return null===this._edgeList&&(this._edgeList=new fh(this._edgeMap.values())),this._edgeList},Om.prototype.getLocation=function(t,e,n){return this._ptInAreaLocation[t]===yf.NONE&&(this._ptInAreaLocation[t]=Dm.locate(e,n[t].getGeometry())),this._ptInAreaLocation[t]},Om.prototype.toString=function(){var t=new xf;t.append("EdgeEndStar:   "+this.getCoordinate()),t.append("\n");for(var e=this.iterator();e.hasNext();){var n=e.next();t.append(n),t.append("\n")}return t.toString()},Om.prototype.computeEdgeEndLabels=function(t){for(var e=this.iterator();e.hasNext();){e.next().computeLabel(t)}},Om.prototype.computeLabelling=function(t){var e=this;this.computeEdgeEndLabels(t[0].getBoundaryNodeRule()),this.propagateSideLabels(0),this.propagateSideLabels(1);for(var n=[!1,!1],r=this.iterator();r.hasNext();)for(var o=r.next(),i=o.getLabel(),a=0;a<2;a++)i.isLine(a)&&i.getLocation(a)===yf.BOUNDARY&&(n[a]=!0);for(var s=this.iterator();s.hasNext();)for(var c=s.next(),u=c.getLabel(),l=0;l<2;l++)if(u.isAnyNull(l)){var d=yf.NONE;if(n[l])d=yf.EXTERIOR;else{var p=c.getCoordinate();d=e.getLocation(l,p,t)}u.setAllLocationsIfNull(l,d)}},Om.prototype.getDegree=function(){return this._edgeMap.size()},Om.prototype.insertEdgeEnd=function(t,e){this._edgeMap.put(t,e),this._edgeList=null},Om.prototype.interfaces_=function(){return[]},Om.prototype.getClass=function(){return Om};var Tm=function(t){function e(){t.call(this),this._resultAreaEdgeList=null,this._label=null,this._SCANNING_FOR_INCOMING=1,this._LINKING_TO_OUTGOING=2}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.linkResultDirectedEdges=function(){var t=this;this.getResultAreaEdges();for(var e=null,n=null,r=this._SCANNING_FOR_INCOMING,o=0;o<this._resultAreaEdgeList.size();o++){var i=t._resultAreaEdgeList.get(o),a=i.getSym();if(i.getLabel().isArea())switch(null===e&&i.isInResult()&&(e=i),r){case t._SCANNING_FOR_INCOMING:if(!a.isInResult())continue;n=a,r=t._LINKING_TO_OUTGOING;break;case t._LINKING_TO_OUTGOING:if(!i.isInResult())continue;n.setNext(i),r=t._SCANNING_FOR_INCOMING}}if(r===this._LINKING_TO_OUTGOING){if(null===e)throw new _g("no outgoing dirEdge found",this.getCoordinate());jf.isTrue(e.isInResult(),"unable to link last incoming dirEdge"),n.setNext(e)}},e.prototype.insert=function(t){var e=t;this.insertEdgeEnd(e,e)},e.prototype.getRightmostEdge=function(){var t=this.getEdges(),e=t.size();if(e<1)return null;var n=t.get(0);if(1===e)return n;var r=t.get(e-1),o=n.getQuadrant(),i=r.getQuadrant();return Mg.isNorthern(o)&&Mg.isNorthern(i)?n:Mg.isNorthern(o)||Mg.isNorthern(i)?0!==n.getDy()?n:0!==r.getDy()?r:(jf.shouldNeverReachHere("found two horizontal edges incident on node"),null):r},e.prototype.print=function(t){Of.out.println("DirectedEdgeStar: "+this.getCoordinate());for(var e=this.iterator();e.hasNext();){var n=e.next();t.print("out "),n.print(t),t.println(),t.print("in "),n.getSym().print(t),t.println()}},e.prototype.getResultAreaEdges=function(){var t=this;if(null!==this._resultAreaEdgeList)return this._resultAreaEdgeList;this._resultAreaEdgeList=new fh;for(var e=this.iterator();e.hasNext();){var n=e.next();(n.isInResult()||n.getSym().isInResult())&&t._resultAreaEdgeList.add(n)}return this._resultAreaEdgeList},e.prototype.updateLabelling=function(t){for(var e=this.iterator();e.hasNext();){var n=e.next(),r=n.getLabel();r.setAllLocationsIfNull(0,t.getLocation(0)),r.setAllLocationsIfNull(1,t.getLocation(1))}},e.prototype.linkAllDirectedEdges=function(){var t=this;this.getEdges();for(var e=null,n=null,r=this._edgeList.size()-1;r>=0;r--){var o=t._edgeList.get(r),i=o.getSym();null===n&&(n=i),null!==e&&i.setNext(e),e=o}n.setNext(e)},e.prototype.computeDepths=function(){var t=this;if(1===arguments.length){var e=arguments[0],n=this.findIndex(e),r=e.getDepth(mg.LEFT),o=e.getDepth(mg.RIGHT),i=this.computeDepths(n+1,this._edgeList.size(),r);if(this.computeDepths(0,n,i)!==o)throw new _g("depth mismatch at "+e.getCoordinate())}else if(3===arguments.length){for(var a=arguments[0],s=arguments[1],c=arguments[2],u=c,l=a;l<s;l++){var d=t._edgeList.get(l);d.setEdgeDepths(mg.RIGHT,u),u=d.getDepth(mg.LEFT)}return u}},e.prototype.mergeSymLabels=function(){for(var t=this.iterator();t.hasNext();){var e=t.next();e.getLabel().merge(e.getSym().getLabel())}},e.prototype.linkMinimalDirectedEdges=function(t){for(var e=this,n=null,r=null,o=this._SCANNING_FOR_INCOMING,i=this._resultAreaEdgeList.size()-1;i>=0;i--){var a=e._resultAreaEdgeList.get(i),s=a.getSym();switch(null===n&&a.getEdgeRing()===t&&(n=a),o){case e._SCANNING_FOR_INCOMING:if(s.getEdgeRing()!==t)continue;r=s,o=e._LINKING_TO_OUTGOING;break;case e._LINKING_TO_OUTGOING:if(a.getEdgeRing()!==t)continue;r.setNextMin(a),o=e._SCANNING_FOR_INCOMING}}o===this._LINKING_TO_OUTGOING&&(jf.isTrue(null!==n,"found null for first outgoing dirEdge"),jf.isTrue(n.getEdgeRing()===t,"unable to link last incoming dirEdge"),r.setNextMin(n))},e.prototype.getOutgoingDegree=function(){if(0===arguments.length){for(var t=0,e=this.iterator();e.hasNext();){e.next().isInResult()&&t++}return t}if(1===arguments.length){for(var n=arguments[0],r=0,o=this.iterator();o.hasNext();){o.next().getEdgeRing()===n&&r++}return r}},e.prototype.getLabel=function(){return this._label},e.prototype.findCoveredLineEdges=function(){
for(var t=yf.NONE,e=this.iterator();e.hasNext();){var n=e.next(),r=n.getSym();if(!n.isLineEdge()){if(n.isInResult()){t=yf.INTERIOR;break}if(r.isInResult()){t=yf.EXTERIOR;break}}}if(t===yf.NONE)return null;for(var o=t,i=this.iterator();i.hasNext();){var a=i.next(),s=a.getSym();a.isLineEdge()?a.getEdge().setCovered(o===yf.INTERIOR):(a.isInResult()&&(o=yf.EXTERIOR),s.isInResult()&&(o=yf.INTERIOR))}},e.prototype.computeLabelling=function(e){var n=this;t.prototype.computeLabelling.call(this,e),this._label=new Eg(yf.NONE);for(var r=this.iterator();r.hasNext();)for(var o=r.next(),i=o.getEdge(),a=i.getLabel(),s=0;s<2;s++){var c=a.getLocation(s);c!==yf.INTERIOR&&c!==yf.BOUNDARY||n._label.setLocation(s,yf.INTERIOR)}},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(Om),Rm=function(t){function e(){t.apply(this,arguments)}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.createNode=function(t){return new Ag(t,new Tm)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(Tg),Fm=function t(){this._pts=null,this._orientation=null;var e=arguments[0];this._pts=e,this._orientation=t.orientation(e)};Fm.prototype.compareTo=function(t){var e=t;return Fm.compareOriented(this._pts,this._orientation,e._pts,e._orientation)},Fm.prototype.interfaces_=function(){return[lf]},Fm.prototype.getClass=function(){return Fm},Fm.orientation=function(t){return 1===mh.increasingDirection(t)},Fm.compareOriented=function(t,e,n,r){for(var o=e?1:-1,i=r?1:-1,a=e?t.length:-1,s=r?n.length:-1,c=e?0:t.length-1,u=r?0:n.length-1;;){var l=t[c].compareTo(n[u]);if(0!==l)return l;c+=o,u+=i;var d=c===a,p=u===s;if(d&&!p)return-1;if(!d&&p)return 1;if(d&&p)return 0}};var Gm=function(){this._edges=new fh,this._ocaMap=new hc};Gm.prototype.print=function(t){var e=this;t.print("MULTILINESTRING ( ");for(var n=0;n<this._edges.size();n++){var r=e._edges.get(n);n>0&&t.print(","),t.print("(");for(var o=r.getCoordinates(),i=0;i<o.length;i++)i>0&&t.print(","),t.print(o[i].x+" "+o[i].y);t.println(")")}t.print(")  ")},Gm.prototype.addAll=function(t){for(var e=this,n=t.iterator();n.hasNext();)e.add(n.next())},Gm.prototype.findEdgeIndex=function(t){for(var e=this,n=0;n<this._edges.size();n++)if(e._edges.get(n).equals(t))return n;return-1},Gm.prototype.iterator=function(){return this._edges.iterator()},Gm.prototype.getEdges=function(){return this._edges},Gm.prototype.get=function(t){return this._edges.get(t)},Gm.prototype.findEqualEdge=function(t){var e=new Fm(t.getCoordinates());return this._ocaMap.get(e)},Gm.prototype.add=function(t){this._edges.add(t);var e=new Fm(t.getCoordinates());this._ocaMap.put(e,t)},Gm.prototype.interfaces_=function(){return[]},Gm.prototype.getClass=function(){return Gm};var qm=function(){};qm.prototype.processIntersections=function(t,e,n,r){},qm.prototype.isDone=function(){},qm.prototype.interfaces_=function(){return[]},qm.prototype.getClass=function(){return qm};var km=function(){this._hasIntersection=!1,this._hasProper=!1,this._hasProperInterior=!1,this._hasInterior=!1,this._properIntersectionPoint=null,this._li=null,this._isSelfIntersection=null,this.numIntersections=0,this.numInteriorIntersections=0,this.numProperIntersections=0,this.numTests=0;var t=arguments[0];this._li=t};km.prototype.isTrivialIntersection=function(t,e,n,r){if(t===n&&1===this._li.getIntersectionNum()){if(km.isAdjacentSegments(e,r))return!0;if(t.isClosed()){var o=t.size()-1;if(0===e&&r===o||0===r&&e===o)return!0}}return!1},km.prototype.getProperIntersectionPoint=function(){return this._properIntersectionPoint},km.prototype.hasProperInteriorIntersection=function(){return this._hasProperInterior},km.prototype.getLineIntersector=function(){return this._li},km.prototype.hasProperIntersection=function(){return this._hasProper},km.prototype.processIntersections=function(t,e,n,r){if(t===n&&e===r)return null;this.numTests++;var o=t.getCoordinates()[e],i=t.getCoordinates()[e+1],a=n.getCoordinates()[r],s=n.getCoordinates()[r+1];this._li.computeIntersection(o,i,a,s),this._li.hasIntersection()&&(this.numIntersections++,this._li.isInteriorIntersection()&&(this.numInteriorIntersections++,this._hasInterior=!0),this.isTrivialIntersection(t,e,n,r)||(this._hasIntersection=!0,t.addIntersections(this._li,e,0),n.addIntersections(this._li,r,1),this._li.isProper()&&(this.numProperIntersections++,this._hasProper=!0,this._hasProperInterior=!0)))},km.prototype.hasIntersection=function(){return this._hasIntersection},km.prototype.isDone=function(){return!1},km.prototype.hasInteriorIntersection=function(){return this._hasInterior},km.prototype.interfaces_=function(){return[qm]},km.prototype.getClass=function(){return km},km.isAdjacentSegments=function(t,e){return 1===Math.abs(t-e)};var Um=function(){this.coord=null,this.segmentIndex=null,this.dist=null;var t=arguments[0],e=arguments[1],n=arguments[2];this.coord=new ff(t),this.segmentIndex=e,this.dist=n};Um.prototype.getSegmentIndex=function(){return this.segmentIndex},Um.prototype.getCoordinate=function(){return this.coord},Um.prototype.print=function(t){t.print(this.coord),t.print(" seg # = "+this.segmentIndex),t.println(" dist = "+this.dist)},Um.prototype.compareTo=function(t){var e=t;return this.compare(e.segmentIndex,e.dist)},Um.prototype.isEndPoint=function(t){return 0===this.segmentIndex&&0===this.dist||this.segmentIndex===t},Um.prototype.toString=function(){return this.coord+" seg # = "+this.segmentIndex+" dist = "+this.dist},Um.prototype.getDistance=function(){return this.dist},Um.prototype.compare=function(t,e){return this.segmentIndex<t?-1:this.segmentIndex>t?1:this.dist<e?-1:this.dist>e?1:0},Um.prototype.interfaces_=function(){return[lf]},Um.prototype.getClass=function(){return Um};var Bm=function(){this._nodeMap=new hc,this.edge=null;var t=arguments[0];this.edge=t};Bm.prototype.print=function(t){t.println("Intersections:");for(var e=this.iterator();e.hasNext();){e.next().print(t)}},Bm.prototype.iterator=function(){return this._nodeMap.values().iterator()},Bm.prototype.addSplitEdges=function(t){var e=this;this.addEndpoints();for(var n=this.iterator(),r=n.next();n.hasNext();){var o=n.next(),i=e.createSplitEdge(r,o);t.add(i),r=o}},Bm.prototype.addEndpoints=function(){var t=this.edge.pts.length-1;this.add(this.edge.pts[0],0,0),this.add(this.edge.pts[t],t,0)},Bm.prototype.createSplitEdge=function(t,e){var n=this,r=e.segmentIndex-t.segmentIndex+2,o=this.edge.pts[e.segmentIndex],i=e.dist>0||!e.coord.equals2D(o);i||r--;var a=new Array(r).fill(null),s=0;a[s++]=new ff(t.coord);for(var c=t.segmentIndex+1;c<=e.segmentIndex;c++)a[s++]=n.edge.pts[c];return i&&(a[s]=e.coord),new Xm(a,new Eg(this.edge._label))},Bm.prototype.add=function(t,e,n){var r=new Um(t,e,n),o=this._nodeMap.get(r);return null!==o?o:(this._nodeMap.put(r,r),r)},Bm.prototype.isIntersection=function(t){for(var e=this.iterator();e.hasNext();){if(e.next().coord.equals(t))return!0}return!1},Bm.prototype.interfaces_=function(){return[]},Bm.prototype.getClass=function(){return Bm};var Vm=function(){};Vm.prototype.getChainStartIndices=function(t){var e=this,n=0,r=new fh;r.add(new Ef(n));do{var o=e.findChainEnd(t,n);r.add(new Ef(o)),n=o}while(n<t.length-1);return Vm.toIntArray(r)},Vm.prototype.findChainEnd=function(t,e){for(var n=Mg.quadrant(t[e],t[e+1]),r=e+1;r<t.length;){if(Mg.quadrant(t[r-1],t[r])!==n)break;r++}return r-1},Vm.prototype.interfaces_=function(){return[]},Vm.prototype.getClass=function(){return Vm},Vm.toIntArray=function(t){for(var e=new Array(t.size()).fill(null),n=0;n<e.length;n++)e[n]=t.get(n).intValue();return e};var zm=function(){this.e=null,this.pts=null,this.startIndex=null,this.env1=new Rf,this.env2=new Rf;var t=arguments[0];this.e=t,this.pts=t.getCoordinates();var e=new Vm;this.startIndex=e.getChainStartIndices(this.pts)};zm.prototype.getCoordinates=function(){return this.pts},zm.prototype.getMaxX=function(t){var e=this.pts[this.startIndex[t]].x,n=this.pts[this.startIndex[t+1]].x;return e>n?e:n},zm.prototype.getMinX=function(t){var e=this.pts[this.startIndex[t]].x,n=this.pts[this.startIndex[t+1]].x;return e<n?e:n},zm.prototype.computeIntersectsForChain=function(){if(4===arguments.length){var t=arguments[0],e=arguments[1],n=arguments[2],r=arguments[3];this.computeIntersectsForChain(this.startIndex[t],this.startIndex[t+1],e,e.startIndex[n],e.startIndex[n+1],r)}else if(6===arguments.length){var o=arguments[0],i=arguments[1],a=arguments[2],s=arguments[3],c=arguments[4],u=arguments[5],l=this.pts[o],d=this.pts[i],p=a.pts[s],f=a.pts[c];if(i-o==1&&c-s==1)return u.addIntersections(this.e,o,a.e,s),null;if(this.env1.init(l,d),this.env2.init(p,f),!this.env1.intersects(this.env2))return null;var h=Math.trunc((o+i)/2),g=Math.trunc((s+c)/2);o<h&&(s<g&&this.computeIntersectsForChain(o,h,a,s,g,u),g<c&&this.computeIntersectsForChain(o,h,a,g,c,u)),h<i&&(s<g&&this.computeIntersectsForChain(h,i,a,s,g,u),g<c&&this.computeIntersectsForChain(h,i,a,g,c,u))}},zm.prototype.getStartIndexes=function(){return this.startIndex},zm.prototype.computeIntersects=function(t,e){for(var n=this,r=0;r<this.startIndex.length-1;r++)for(var o=0;o<t.startIndex.length-1;o++)n.computeIntersectsForChain(r,t,o,e)},zm.prototype.interfaces_=function(){return[]},zm.prototype.getClass=function(){return zm};var jm=function t(){var e=this;this._depth=Array(2).fill().map(function(){return Array(3)});for(var n=0;n<2;n++)for(var r=0;r<3;r++)e._depth[n][r]=t.NULL_VALUE},Ym={NULL_VALUE:{configurable:!0}};jm.prototype.getDepth=function(t,e){return this._depth[t][e]},jm.prototype.setDepth=function(t,e,n){this._depth[t][e]=n},jm.prototype.isNull=function(){var t=this;if(0===arguments.length){for(var e=0;e<2;e++)for(var n=0;n<3;n++)if(t._depth[e][n]!==jm.NULL_VALUE)return!1;return!0}if(1===arguments.length){var r=arguments[0];return this._depth[r][1]===jm.NULL_VALUE}if(2===arguments.length){var o=arguments[0],i=arguments[1];return this._depth[o][i]===jm.NULL_VALUE}},jm.prototype.normalize=function(){for(var t=this,e=0;e<2;e++)if(!t.isNull(e)){var n=t._depth[e][1];t._depth[e][2]<n&&(n=t._depth[e][2]),n<0&&(n=0);for(var r=1;r<3;r++){var o=0;t._depth[e][r]>n&&(o=1),t._depth[e][r]=o}}},jm.prototype.getDelta=function(t){return this._depth[t][mg.RIGHT]-this._depth[t][mg.LEFT]},jm.prototype.getLocation=function(t,e){return this._depth[t][e]<=0?yf.EXTERIOR:yf.INTERIOR},jm.prototype.toString=function(){return"A: "+this._depth[0][1]+","+this._depth[0][2]+" B: "+this._depth[1][1]+","+this._depth[1][2]},jm.prototype.add=function(){var t=this;if(1===arguments.length)for(var e=arguments[0],n=0;n<2;n++)for(var r=1;r<3;r++){var o=e.getLocation(n,r);o!==yf.EXTERIOR&&o!==yf.INTERIOR||(t.isNull(n,r)?t._depth[n][r]=jm.depthAtLocation(o):t._depth[n][r]+=jm.depthAtLocation(o))}else if(3===arguments.length){var i=arguments[0],a=arguments[1],s=arguments[2];s===yf.INTERIOR&&this._depth[i][a]++}},jm.prototype.interfaces_=function(){return[]},jm.prototype.getClass=function(){return jm},jm.depthAtLocation=function(t){return t===yf.EXTERIOR?0:t===yf.INTERIOR?1:jm.NULL_VALUE},Ym.NULL_VALUE.get=function(){return-1},Object.defineProperties(jm,Ym);var Xm=function(t){function e(){if(t.call(this),this.pts=null,this._env=null,this.eiList=new Bm(this),this._name=null,this._mce=null,this._isIsolated=!0,this._depth=new jm,this._depthDelta=0,1===arguments.length){var n=arguments[0];e.call(this,n,null)}else if(2===arguments.length){var r=arguments[0],o=arguments[1];this.pts=r,this._label=o}}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.getDepth=function(){return this._depth},e.prototype.getCollapsedEdge=function(){var t=new Array(2).fill(null);return t[0]=this.pts[0],t[1]=this.pts[1],new e(t,Eg.toLineLabel(this._label))},e.prototype.isIsolated=function(){return this._isIsolated},e.prototype.getCoordinates=function(){return this.pts},e.prototype.setIsolated=function(t){this._isIsolated=t},e.prototype.setName=function(t){this._name=t},e.prototype.equals=function(t){var n=this;if(!(t instanceof e))return!1;var r=t;if(this.pts.length!==r.pts.length)return!1;for(var o=!0,i=!0,a=this.pts.length,s=0;s<this.pts.length;s++)if(n.pts[s].equals2D(r.pts[s])||(o=!1),n.pts[s].equals2D(r.pts[--a])||(i=!1),!o&&!i)return!1;return!0},e.prototype.getCoordinate=function(){if(0===arguments.length)return this.pts.length>0?this.pts[0]:null;if(1===arguments.length){var t=arguments[0];return this.pts[t]}},e.prototype.print=function(t){var e=this;t.print("edge "+this._name+": "),t.print("LINESTRING (");for(var n=0;n<this.pts.length;n++)n>0&&t.print(","),t.print(e.pts[n].x+" "+e.pts[n].y);t.print(")  "+this._label+" "+this._depthDelta)},e.prototype.computeIM=function(t){e.updateIM(this._label,t)},e.prototype.isCollapsed=function(){return!!this._label.isArea()&&(3===this.pts.length&&!!this.pts[0].equals(this.pts[2]))},e.prototype.isClosed=function(){return this.pts[0].equals(this.pts[this.pts.length-1])},e.prototype.getMaximumSegmentIndex=function(){return this.pts.length-1},e.prototype.getDepthDelta=function(){return this._depthDelta},e.prototype.getNumPoints=function(){return this.pts.length},e.prototype.printReverse=function(t){var e=this;t.print("edge "+this._name+": ");for(var n=this.pts.length-1;n>=0;n--)t.print(e.pts[n]+" ");t.println("")},e.prototype.getMonotoneChainEdge=function(){return null===this._mce&&(this._mce=new zm(this)),this._mce},e.prototype.getEnvelope=function(){var t=this;if(null===this._env){this._env=new Rf;for(var e=0;e<this.pts.length;e++)t._env.expandToInclude(t.pts[e])}return this._env},e.prototype.addIntersection=function(t,e,n,r){var o=new ff(t.getIntersection(r)),i=e,a=t.getEdgeDistance(n,r),s=i+1;if(s<this.pts.length){var c=this.pts[s];o.equals2D(c)&&(i=s,a=0)}this.eiList.add(o,i,a)},e.prototype.toString=function(){var t=this,e=new xf;e.append("edge "+this._name+": "),e.append("LINESTRING (");for(var n=0;n<this.pts.length;n++)n>0&&e.append(","),e.append(t.pts[n].x+" "+t.pts[n].y);return e.append(")  "+this._label+" "+this._depthDelta),e.toString()},e.prototype.isPointwiseEqual=function(t){var e=this;if(this.pts.length!==t.pts.length)return!1;for(var n=0;n<this.pts.length;n++)if(!e.pts[n].equals2D(t.pts[n]))return!1;return!0},e.prototype.setDepthDelta=function(t){this._depthDelta=t},e.prototype.getEdgeIntersectionList=function(){return this.eiList},e.prototype.addIntersections=function(t,e,n){for(var r=this,o=0;o<t.getIntersectionNum();o++)r.addIntersection(t,e,n,o)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e.updateIM=function(){if(2!==arguments.length)return t.prototype.updateIM.apply(this,arguments);var e=arguments[0],n=arguments[1];n.setAtLeastIfValid(e.getLocation(0,mg.ON),e.getLocation(1,mg.ON),1),e.isArea()&&(n.setAtLeastIfValid(e.getLocation(0,mg.LEFT),e.getLocation(1,mg.LEFT),2),n.setAtLeastIfValid(e.getLocation(0,mg.RIGHT),e.getLocation(1,mg.RIGHT),2))},e}(Pg),Wm=function(t){this._workingPrecisionModel=null,this._workingNoder=null,this._geomFact=null,this._graph=null,this._edgeList=new Gm,this._bufParams=t||null};Wm.prototype.setWorkingPrecisionModel=function(t){this._workingPrecisionModel=t},Wm.prototype.insertUniqueEdge=function(t){var e=this._edgeList.findEqualEdge(t);if(null!==e){var n=e.getLabel(),r=t.getLabel();e.isPointwiseEqual(t)||(r=new Eg(t.getLabel()),r.flip()),n.merge(r);var o=Wm.depthDelta(r),i=e.getDepthDelta(),a=i+o;e.setDepthDelta(a)}else this._edgeList.add(t),t.setDepthDelta(Wm.depthDelta(t.getLabel()))},Wm.prototype.buildSubgraphs=function(t,e){for(var n=new fh,r=t.iterator();r.hasNext();){var o=r.next(),i=o.getRightmostCoordinate(),a=new Sm(n),s=a.getDepth(i);o.computeDepth(s),o.findResultEdges(),n.add(o),e.add(o.getDirectedEdges(),o.getNodes())}},Wm.prototype.createSubgraphs=function(t){for(var e=new fh,n=t.getNodes().iterator();n.hasNext();){var r=n.next();if(!r.isVisited()){var o=new wg;o.create(r),e.add(o)}}return jg.sort(e,jg.reverseOrder()),e},Wm.prototype.createEmptyResultGeometry=function(){return this._geomFact.createPolygon()},Wm.prototype.getNoder=function(t){if(null!==this._workingNoder)return this._workingNoder;var e=new pm,n=new Wf;return n.setPrecisionModel(t),e.setSegmentIntersector(new km(n)),e},Wm.prototype.buffer=function(t,e){var n=this._workingPrecisionModel;null===n&&(n=t.getPrecisionModel()),this._geomFact=t.getFactory();var r=new Cm(n,this._bufParams),o=new Nm(t,e,r),i=o.getCurves();if(i.size()<=0)return this.createEmptyResultGeometry();this.computeNodedEdges(i,n),this._graph=new Rg(new Rm),this._graph.addEdges(this._edgeList.getEdges());var a=this.createSubgraphs(this._graph),s=new Fg(this._geomFact);this.buildSubgraphs(a,s);var c=s.getPolygons();return c.size()<=0?this.createEmptyResultGeometry():this._geomFact.buildGeometry(c)},Wm.prototype.computeNodedEdges=function(t,e){var n=this,r=this.getNoder(e);r.computeNodes(t);for(var o=r.getNodedSubstrings(),i=o.iterator();i.hasNext();){var a=i.next(),s=a.getCoordinates();if(2!==s.length||!s[0].equals2D(s[1])){var c=a.getData(),u=new Xm(a.getCoordinates(),new Eg(c));n.insertUniqueEdge(u)}}},Wm.prototype.setNoder=function(t){this._workingNoder=t},Wm.prototype.interfaces_=function(){return[]},Wm.prototype.getClass=function(){return Wm},Wm.depthDelta=function(t){var e=t.getLocation(0,mg.LEFT),n=t.getLocation(0,mg.RIGHT);return e===yf.INTERIOR&&n===yf.EXTERIOR?1:e===yf.EXTERIOR&&n===yf.INTERIOR?-1:0},Wm.convertSegStrings=function(t){for(var e=new cg,n=new fh;t.hasNext();){var r=t.next(),o=e.createLineString(r.getCoordinates());n.add(o)}return e.buildGeometry(n)};var $m=function(){if(this._noder=null,this._scaleFactor=null,this._offsetX=null,this._offsetY=null,this._isScaled=!1,2===arguments.length){var t=arguments[0],e=arguments[1];this._noder=t,this._scaleFactor=e,this._offsetX=0,this._offsetY=0,this._isScaled=!this.isIntegerPrecision()}else if(4===arguments.length){var n=arguments[0],r=arguments[1],o=arguments[2],i=arguments[3];this._noder=n,this._scaleFactor=r,this._offsetX=o,this._offsetY=i,this._isScaled=!this.isIntegerPrecision()}};$m.prototype.rescale=function(){var t=this;if(_f(arguments[0],uh))for(var e=arguments[0],n=e.iterator();n.hasNext();){var r=n.next();t.rescale(r.getCoordinates())}else if(arguments[0]instanceof Array){for(var o=arguments[0],i=0;i<o.length;i++)o[i].x=o[i].x/t._scaleFactor+t._offsetX,o[i].y=o[i].y/t._scaleFactor+t._offsetY;2===o.length&&o[0].equals2D(o[1])&&Of.out.println(o)}},$m.prototype.scale=function(){var t=this;if(_f(arguments[0],uh)){for(var e=arguments[0],n=new fh,r=e.iterator();r.hasNext();){var o=r.next();n.add(new om(t.scale(o.getCoordinates()),o.getData()))}return n}if(arguments[0]instanceof Array){for(var i=arguments[0],a=new Array(i.length).fill(null),s=0;s<i.length;s++)a[s]=new ff(Math.round((i[s].x-t._offsetX)*t._scaleFactor),Math.round((i[s].y-t._offsetY)*t._scaleFactor),i[s].z);return mh.removeRepeatedPoints(a)}},$m.prototype.isIntegerPrecision=function(){return 1===this._scaleFactor},$m.prototype.getNodedSubstrings=function(){var t=this._noder.getNodedSubstrings();return this._isScaled&&this.rescale(t),t},$m.prototype.computeNodes=function(t){var e=t;this._isScaled&&(e=this.scale(t)),this._noder.computeNodes(e)},$m.prototype.interfaces_=function(){return[lm]},$m.prototype.getClass=function(){return $m};var Hm=function(){this._li=new Wf,this._segStrings=null;var t=arguments[0];this._segStrings=t},Km={fact:{configurable:!0}};Hm.prototype.checkEndPtVertexIntersections=function(){var t=this;if(0===arguments.length)for(var e=this._segStrings.iterator();e.hasNext();){var n=e.next(),r=n.getCoordinates();t.checkEndPtVertexIntersections(r[0],t._segStrings),t.checkEndPtVertexIntersections(r[r.length-1],t._segStrings)}else if(2===arguments.length)for(var o=arguments[0],i=arguments[1],a=i.iterator();a.hasNext();)for(var s=a.next(),c=s.getCoordinates(),u=1;u<c.length-1;u++)if(c[u].equals(o))throw new Vf("found endpt/interior pt intersection at index "+u+" :pt "+o)},Hm.prototype.checkInteriorIntersections=function(){var t=this;if(0===arguments.length)for(var e=this._segStrings.iterator();e.hasNext();)for(var n=e.next(),r=this._segStrings.iterator();r.hasNext();){var o=r.next();t.checkInteriorIntersections(n,o)}else if(2===arguments.length)for(var i=arguments[0],a=arguments[1],s=i.getCoordinates(),c=a.getCoordinates(),u=0;u<s.length-1;u++)for(var l=0;l<c.length-1;l++)t.checkInteriorIntersections(i,u,a,l);else if(4===arguments.length){var d=arguments[0],p=arguments[1],f=arguments[2],h=arguments[3];if(d===f&&p===h)return null;var g=d.getCoordinates()[p],m=d.getCoordinates()[p+1],y=f.getCoordinates()[h],v=f.getCoordinates()[h+1];if(this._li.computeIntersection(g,m,y,v),this._li.hasIntersection()&&(this._li.isProper()||this.hasInteriorIntersection(this._li,g,m)||this.hasInteriorIntersection(this._li,y,v)))throw new Vf("found non-noded intersection at "+g+"-"+m+" and "+y+"-"+v)}},Hm.prototype.checkValid=function(){this.checkEndPtVertexIntersections(),this.checkInteriorIntersections(),this.checkCollapses()},Hm.prototype.checkCollapses=function(){var t=this;if(0===arguments.length)for(var e=this._segStrings.iterator();e.hasNext();){var n=e.next();t.checkCollapses(n)}else if(1===arguments.length)for(var r=arguments[0],o=r.getCoordinates(),i=0;i<o.length-2;i++)t.checkCollapse(o[i],o[i+1],o[i+2])},Hm.prototype.hasInteriorIntersection=function(t,e,n){for(var r=0;r<t.getIntersectionNum();r++){var o=t.getIntersection(r);if(!o.equals(e)&&!o.equals(n))return!0}return!1},Hm.prototype.checkCollapse=function(t,e,n){if(t.equals(n))throw new Vf("found non-noded collapse at "+Hm.fact.createLineString([t,e,n]))},Hm.prototype.interfaces_=function(){return[]},Hm.prototype.getClass=function(){return Hm},Km.fact.get=function(){return new cg},Object.defineProperties(Hm,Km);var Jm=function(){this._li=null,this._pt=null,this._originalPt=null,this._ptScaled=null,this._p0Scaled=null,this._p1Scaled=null,this._scaleFactor=null,this._minx=null,this._maxx=null,this._miny=null,this._maxy=null,this._corner=new Array(4).fill(null),this._safeEnv=null;var t=arguments[0],e=arguments[1],n=arguments[2];if(this._originalPt=t,this._pt=t,this._scaleFactor=e,this._li=n,e<=0)throw new sf("Scale factor must be non-zero");1!==e&&(this._pt=new ff(this.scale(t.x),this.scale(t.y)),this._p0Scaled=new ff,this._p1Scaled=new ff),this.initCorners(this._pt)},Zm={SAFE_ENV_EXPANSION_FACTOR:{configurable:!0}};Jm.prototype.intersectsScaled=function(t,e){var n=Math.min(t.x,e.x),r=Math.max(t.x,e.x),o=Math.min(t.y,e.y),i=Math.max(t.y,e.y),a=this._maxx<n||this._minx>r||this._maxy<o||this._miny>i;if(a)return!1;var s=this.intersectsToleranceSquare(t,e);return jf.isTrue(!(a&&s),"Found bad envelope test"),s},Jm.prototype.initCorners=function(t){this._minx=t.x-.5,this._maxx=t.x+.5,this._miny=t.y-.5,this._maxy=t.y+.5,this._corner[0]=new ff(this._maxx,this._maxy),this._corner[1]=new ff(this._minx,this._maxy),this._corner[2]=new ff(this._minx,this._miny),this._corner[3]=new ff(this._maxx,this._miny)},Jm.prototype.intersects=function(t,e){return 1===this._scaleFactor?this.intersectsScaled(t,e):(this.copyScaled(t,this._p0Scaled),this.copyScaled(e,this._p1Scaled),this.intersectsScaled(this._p0Scaled,this._p1Scaled))},Jm.prototype.scale=function(t){return Math.round(t*this._scaleFactor)},Jm.prototype.getCoordinate=function(){return this._originalPt},Jm.prototype.copyScaled=function(t,e){e.x=this.scale(t.x),e.y=this.scale(t.y)},Jm.prototype.getSafeEnvelope=function(){if(null===this._safeEnv){var t=Jm.SAFE_ENV_EXPANSION_FACTOR/this._scaleFactor;this._safeEnv=new Rf(this._originalPt.x-t,this._originalPt.x+t,this._originalPt.y-t,this._originalPt.y+t)}return this._safeEnv},Jm.prototype.intersectsPixelClosure=function(t,e){return this._li.computeIntersection(t,e,this._corner[0],this._corner[1]),!!this._li.hasIntersection()||(this._li.computeIntersection(t,e,this._corner[1],this._corner[2]),!!this._li.hasIntersection()||(this._li.computeIntersection(t,e,this._corner[2],this._corner[3]),!!this._li.hasIntersection()||(this._li.computeIntersection(t,e,this._corner[3],this._corner[0]),!!this._li.hasIntersection())))},Jm.prototype.intersectsToleranceSquare=function(t,e){var n=!1,r=!1;return this._li.computeIntersection(t,e,this._corner[0],this._corner[1]),!!this._li.isProper()||(this._li.computeIntersection(t,e,this._corner[1],this._corner[2]),!!this._li.isProper()||(this._li.hasIntersection()&&(n=!0),this._li.computeIntersection(t,e,this._corner[2],this._corner[3]),!!this._li.isProper()||(this._li.hasIntersection()&&(r=!0),this._li.computeIntersection(t,e,this._corner[3],this._corner[0]),!!this._li.isProper()||(!(!n||!r)||(!!t.equals(this._pt)||!!e.equals(this._pt))))))},Jm.prototype.addSnappedNode=function(t,e){var n=t.getCoordinate(e),r=t.getCoordinate(e+1);return!!this.intersects(n,r)&&(t.addIntersection(this.getCoordinate(),e),!0)},Jm.prototype.interfaces_=function(){return[]},Jm.prototype.getClass=function(){return Jm},Zm.SAFE_ENV_EXPANSION_FACTOR.get=function(){return.75},Object.defineProperties(Jm,Zm);var Qm=function(){this.tempEnv1=new Rf,this.selectedSegment=new im};Qm.prototype.select=function(){if(1===arguments.length);else if(2===arguments.length){var t=arguments[0],e=arguments[1];t.getLineSegment(e,this.selectedSegment),this.select(this.selectedSegment)}},Qm.prototype.interfaces_=function(){return[]},Qm.prototype.getClass=function(){return Qm};var ty=function(){this._index=null;var t=arguments[0];this._index=t},ey={HotPixelSnapAction:{configurable:!0}};ty.prototype.snap=function(){if(1===arguments.length){var t=arguments[0];return this.snap(t,null,-1)}if(3===arguments.length){var e=arguments[0],n=arguments[1],r=arguments[2],o=e.getSafeEnvelope(),i=new ny(e,n,r);return this._index.query(o,{interfaces_:function(){return[Ug]},visitItem:function(t){t.select(o,i)}}),i.isNodeAdded()}},ty.prototype.interfaces_=function(){return[]},ty.prototype.getClass=function(){return ty},ey.HotPixelSnapAction.get=function(){return ny},Object.defineProperties(ty,ey);var ny=function(t){function e(){t.call(this),this._hotPixel=null,this._parentEdge=null,this._hotPixelVertexIndex=null,this._isNodeAdded=!1;var e=arguments[0],n=arguments[1],r=arguments[2];this._hotPixel=e,this._parentEdge=n,this._hotPixelVertexIndex=r}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.isNodeAdded=function(){return this._isNodeAdded},e.prototype.select=function(){if(2!==arguments.length)return t.prototype.select.apply(this,arguments);var e=arguments[0],n=arguments[1],r=e.getContext();if(null!==this._parentEdge&&r===this._parentEdge&&n===this._hotPixelVertexIndex)return null;this._isNodeAdded=this._hotPixel.addSnappedNode(r,n)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(Qm),ry=function(){this._li=null,this._interiorIntersections=null;var t=arguments[0];this._li=t,this._interiorIntersections=new fh};ry.prototype.processIntersections=function(t,e,n,r){var o=this;if(t===n&&e===r)return null;var i=t.getCoordinates()[e],a=t.getCoordinates()[e+1],s=n.getCoordinates()[r],c=n.getCoordinates()[r+1];if(this._li.computeIntersection(i,a,s,c),this._li.hasIntersection()&&this._li.isInteriorIntersection()){for(var u=0;u<this._li.getIntersectionNum();u++)o._interiorIntersections.add(o._li.getIntersection(u));t.addIntersections(this._li,e,0),n.addIntersections(this._li,r,1)}},ry.prototype.isDone=function(){return!1},ry.prototype.getInteriorIntersections=function(){return this._interiorIntersections},ry.prototype.interfaces_=function(){return[qm]},ry.prototype.getClass=function(){return ry};var oy=function(){this._pm=null,this._li=null,this._scaleFactor=null,this._noder=null,this._pointSnapper=null,this._nodedSegStrings=null;var t=arguments[0];this._pm=t,this._li=new Wf,this._li.setPrecisionModel(t),this._scaleFactor=t.getScale()};oy.prototype.checkCorrectness=function(t){var e=om.getNodedSubstrings(t),n=new Hm(e);try{n.checkValid()}catch(t){if(!(t instanceof Lf))throw t;t.printStackTrace()}},oy.prototype.getNodedSubstrings=function(){return om.getNodedSubstrings(this._nodedSegStrings)},oy.prototype.snapRound=function(t,e){var n=this.findInteriorIntersections(t,e);this.computeIntersectionSnaps(n),this.computeVertexSnaps(t)},oy.prototype.findInteriorIntersections=function(t,e){var n=new ry(e);return this._noder.setSegmentIntersector(n),this._noder.computeNodes(t),n.getInteriorIntersections()},oy.prototype.computeVertexSnaps=function(){var t=this;if(_f(arguments[0],uh))for(var e=arguments[0],n=e.iterator();n.hasNext();){var r=n.next();t.computeVertexSnaps(r)}else if(arguments[0]instanceof om)for(var o=arguments[0],i=o.getCoordinates(),a=0;a<i.length;a++){var s=new Jm(i[a],t._scaleFactor,t._li),c=t._pointSnapper.snap(s,o,a);c&&o.addIntersection(i[a],a)}},oy.prototype.computeNodes=function(t){this._nodedSegStrings=t,this._noder=new pm,this._pointSnapper=new ty(this._noder.getIndex()),this.snapRound(t,this._li)},oy.prototype.computeIntersectionSnaps=function(t){for(var e=this,n=t.iterator();n.hasNext();){var r=n.next(),o=new Jm(r,e._scaleFactor,e._li);e._pointSnapper.snap(o)}},oy.prototype.interfaces_=function(){return[lm]},oy.prototype.getClass=function(){return oy};var iy=function(){if(this._argGeom=null,this._distance=null,this._bufParams=new hm,this._resultGeometry=null,this._saveException=null,1===arguments.length){var t=arguments[0];this._argGeom=t}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this._argGeom=e,this._bufParams=n}},ay={CAP_ROUND:{configurable:!0},CAP_BUTT:{configurable:!0},CAP_FLAT:{configurable:!0},CAP_SQUARE:{configurable:!0},MAX_PRECISION_DIGITS:{configurable:!0}};iy.prototype.bufferFixedPrecision=function(t){var e=new $m(new oy(new og(1)),t.getScale()),n=new Wm(this._bufParams);n.setWorkingPrecisionModel(t),n.setNoder(e),this._resultGeometry=n.buffer(this._argGeom,this._distance)},iy.prototype.bufferReducedPrecision=function(){var t=this;if(0===arguments.length){for(var e=iy.MAX_PRECISION_DIGITS;e>=0;e--){try{t.bufferReducedPrecision(e)}catch(e){if(!(e instanceof _g))throw e;t._saveException=e}if(null!==t._resultGeometry)return null}throw this._saveException}if(1===arguments.length){var n=arguments[0],r=iy.precisionScaleFactor(this._argGeom,this._distance,n),o=new og(r);this.bufferFixedPrecision(o)}},iy.prototype.computeGeometry=function(){if(this.bufferOriginalPrecision(),null!==this._resultGeometry)return null;var t=this._argGeom.getFactory().getPrecisionModel();t.getType()===og.FIXED?this.bufferFixedPrecision(t):this.bufferReducedPrecision()},iy.prototype.setQuadrantSegments=function(t){this._bufParams.setQuadrantSegments(t)},iy.prototype.bufferOriginalPrecision=function(){try{var t=new Wm(this._bufParams);this._resultGeometry=t.buffer(this._argGeom,this._distance)}catch(t){if(!(t instanceof Vf))throw t;this._saveException=t}},iy.prototype.getResultGeometry=function(t){return this._distance=t,this.computeGeometry(),this._resultGeometry},iy.prototype.setEndCapStyle=function(t){this._bufParams.setEndCapStyle(t)},iy.prototype.interfaces_=function(){return[]},iy.prototype.getClass=function(){return iy},iy.bufferOp=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return new iy(t).getResultGeometry(e)}if(3===arguments.length){if(Number.isInteger(arguments[2])&&arguments[0]instanceof Qf&&"number"==typeof arguments[1]){var n=arguments[0],r=arguments[1],o=arguments[2],i=new iy(n);i.setQuadrantSegments(o);return i.getResultGeometry(r)}if(arguments[2]instanceof hm&&arguments[0]instanceof Qf&&"number"==typeof arguments[1]){var a=arguments[0],s=arguments[1],c=arguments[2];return new iy(a,c).getResultGeometry(s)}}else if(4===arguments.length){var u=arguments[0],l=arguments[1],d=arguments[2],p=arguments[3],f=new iy(u);f.setQuadrantSegments(d),f.setEndCapStyle(p);var h=f.getResultGeometry(l);return h}},iy.precisionScaleFactor=function(t,e,n){
var r=t.getEnvelopeInternal(),o=bf.max(Math.abs(r.getMaxX()),Math.abs(r.getMaxY()),Math.abs(r.getMinX()),Math.abs(r.getMinY())),i=e>0?e:0,a=o+2*i,s=Math.trunc(Math.log(a)/Math.log(10)+1),c=n-s;return Math.pow(10,c)},ay.CAP_ROUND.get=function(){return hm.CAP_ROUND},ay.CAP_BUTT.get=function(){return hm.CAP_FLAT},ay.CAP_FLAT.get=function(){return hm.CAP_FLAT},ay.CAP_SQUARE.get=function(){return hm.CAP_SQUARE},ay.MAX_PRECISION_DIGITS.get=function(){return 12},Object.defineProperties(iy,ay);var sy=function(){this._pt=[new ff,new ff],this._distance=cf.NaN,this._isNull=!0};sy.prototype.getCoordinates=function(){return this._pt},sy.prototype.getCoordinate=function(t){return this._pt[t]},sy.prototype.setMinimum=function(){if(1===arguments.length){var t=arguments[0];this.setMinimum(t._pt[0],t._pt[1])}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(this._isNull)return this.initialize(e,n),null;var r=e.distance(n);r<this._distance&&this.initialize(e,n,r)}},sy.prototype.initialize=function(){if(0===arguments.length)this._isNull=!0;else if(2===arguments.length){var t=arguments[0],e=arguments[1];this._pt[0].setCoordinate(t),this._pt[1].setCoordinate(e),this._distance=t.distance(e),this._isNull=!1}else if(3===arguments.length){var n=arguments[0],r=arguments[1],o=arguments[2];this._pt[0].setCoordinate(n),this._pt[1].setCoordinate(r),this._distance=o,this._isNull=!1}},sy.prototype.getDistance=function(){return this._distance},sy.prototype.setMaximum=function(){if(1===arguments.length){var t=arguments[0];this.setMaximum(t._pt[0],t._pt[1])}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(this._isNull)return this.initialize(e,n),null;var r=e.distance(n);r>this._distance&&this.initialize(e,n,r)}},sy.prototype.interfaces_=function(){return[]},sy.prototype.getClass=function(){return sy};var cy=function(){};cy.prototype.interfaces_=function(){return[]},cy.prototype.getClass=function(){return cy},cy.computeDistance=function(){if(arguments[2]instanceof sy&&arguments[0]instanceof Uh&&arguments[1]instanceof ff)for(var t=arguments[0],e=arguments[1],n=arguments[2],r=t.getCoordinates(),o=new im,i=0;i<r.length-1;i++){o.setCoordinates(r[i],r[i+1]);var a=o.closestPoint(e);n.setMinimum(a,e)}else if(arguments[2]instanceof sy&&arguments[0]instanceof jh&&arguments[1]instanceof ff){var s=arguments[0],c=arguments[1],u=arguments[2];cy.computeDistance(s.getExteriorRing(),c,u);for(var l=0;l<s.getNumInteriorRing();l++)cy.computeDistance(s.getInteriorRingN(l),c,u)}else if(arguments[2]instanceof sy&&arguments[0]instanceof Qf&&arguments[1]instanceof ff){var d=arguments[0],p=arguments[1],f=arguments[2];if(d instanceof Uh)cy.computeDistance(d,p,f);else if(d instanceof jh)cy.computeDistance(d,p,f);else if(d instanceof Dh)for(var h=d,g=0;g<h.getNumGeometries();g++){var m=h.getGeometryN(g);cy.computeDistance(m,p,f)}else f.setMinimum(d.getCoordinate(),p)}else if(arguments[2]instanceof sy&&arguments[0]instanceof im&&arguments[1]instanceof ff){var y=arguments[0],v=arguments[1],_=arguments[2],b=y.closestPoint(v);_.setMinimum(b,v)}};var uy=function(t){this._maxPtDist=new sy,this._inputGeom=t||null},ly={MaxPointDistanceFilter:{configurable:!0},MaxMidpointDistanceFilter:{configurable:!0}};uy.prototype.computeMaxMidpointDistance=function(t){var e=new py(this._inputGeom);t.apply(e),this._maxPtDist.setMaximum(e.getMaxPointDistance())},uy.prototype.computeMaxVertexDistance=function(t){var e=new dy(this._inputGeom);t.apply(e),this._maxPtDist.setMaximum(e.getMaxPointDistance())},uy.prototype.findDistance=function(t){return this.computeMaxVertexDistance(t),this.computeMaxMidpointDistance(t),this._maxPtDist.getDistance()},uy.prototype.getDistancePoints=function(){return this._maxPtDist},uy.prototype.interfaces_=function(){return[]},uy.prototype.getClass=function(){return uy},ly.MaxPointDistanceFilter.get=function(){return dy},ly.MaxMidpointDistanceFilter.get=function(){return py},Object.defineProperties(uy,ly);var dy=function(t){this._maxPtDist=new sy,this._minPtDist=new sy,this._geom=t||null};dy.prototype.filter=function(t){this._minPtDist.initialize(),cy.computeDistance(this._geom,t,this._minPtDist),this._maxPtDist.setMaximum(this._minPtDist)},dy.prototype.getMaxPointDistance=function(){return this._maxPtDist},dy.prototype.interfaces_=function(){return[nh]},dy.prototype.getClass=function(){return dy};var py=function(t){this._maxPtDist=new sy,this._minPtDist=new sy,this._geom=t||null};py.prototype.filter=function(t,e){if(0===e)return null;var n=t.getCoordinate(e-1),r=t.getCoordinate(e),o=new ff((n.x+r.x)/2,(n.y+r.y)/2);this._minPtDist.initialize(),cy.computeDistance(this._geom,o,this._minPtDist),this._maxPtDist.setMaximum(this._minPtDist)},py.prototype.isDone=function(){return!1},py.prototype.isGeometryChanged=function(){return!1},py.prototype.getMaxPointDistance=function(){return this._maxPtDist},py.prototype.interfaces_=function(){return[Lh]},py.prototype.getClass=function(){return py};var fy=function(t){this._comps=t||null};fy.prototype.filter=function(t){t instanceof jh&&this._comps.add(t)},fy.prototype.interfaces_=function(){return[Mh]},fy.prototype.getClass=function(){return fy},fy.getPolygons=function(){if(1===arguments.length){var t=arguments[0];return fy.getPolygons(t,new fh)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return e instanceof jh?n.add(e):e instanceof Dh&&e.apply(new fy(n)),n}};var hy=function(){if(this._lines=null,this._isForcedToLineString=!1,1===arguments.length){var t=arguments[0];this._lines=t}else if(2===arguments.length){var e=arguments[0],n=arguments[1];this._lines=e,this._isForcedToLineString=n}};hy.prototype.filter=function(t){if(this._isForcedToLineString&&t instanceof Xh){var e=t.getFactory().createLineString(t.getCoordinateSequence());return this._lines.add(e),null}t instanceof Uh&&this._lines.add(t)},hy.prototype.setForceToLineString=function(t){this._isForcedToLineString=t},hy.prototype.interfaces_=function(){return[Zf]},hy.prototype.getClass=function(){return hy},hy.getGeometry=function(){if(1===arguments.length){var t=arguments[0];return t.getFactory().buildGeometry(hy.getLines(t))}if(2===arguments.length){var e=arguments[0],n=arguments[1];return e.getFactory().buildGeometry(hy.getLines(e,n))}},hy.getLines=function(){if(1===arguments.length){var t=arguments[0];return hy.getLines(t,!1)}if(2===arguments.length){if(_f(arguments[0],uh)&&_f(arguments[1],uh)){for(var e=arguments[0],n=arguments[1],r=e.iterator();r.hasNext();){var o=r.next();hy.getLines(o,n)}return n}if(arguments[0]instanceof Qf&&"boolean"==typeof arguments[1]){var i=arguments[0],a=arguments[1],s=new fh;return i.apply(new hy(s,a)),s}if(arguments[0]instanceof Qf&&_f(arguments[1],uh)){var c=arguments[0],u=arguments[1];return c instanceof Uh?u.add(c):c.apply(new hy(u)),u}}else if(3===arguments.length){if("boolean"==typeof arguments[2]&&_f(arguments[0],uh)&&_f(arguments[1],uh)){for(var l=arguments[0],d=arguments[1],p=arguments[2],f=l.iterator();f.hasNext();){var h=f.next();hy.getLines(h,d,p)}return d}if("boolean"==typeof arguments[2]&&arguments[0]instanceof Qf&&_f(arguments[1],uh)){var g=arguments[0],m=arguments[1],y=arguments[2];return g.apply(new hy(m,y)),m}}};var gy=function(){if(this._boundaryRule=rh.OGC_SFS_BOUNDARY_RULE,this._isIn=null,this._numBoundaries=null,0===arguments.length);else if(1===arguments.length){var t=arguments[0];if(null===t)throw new sf("Rule must be non-null");this._boundaryRule=t}};gy.prototype.locateInternal=function(){var t=this;if(arguments[0]instanceof ff&&arguments[1]instanceof jh){var e=arguments[0],n=arguments[1];if(n.isEmpty())return yf.EXTERIOR;var r=n.getExteriorRing(),o=this.locateInPolygonRing(e,r);if(o===yf.EXTERIOR)return yf.EXTERIOR;if(o===yf.BOUNDARY)return yf.BOUNDARY;for(var i=0;i<n.getNumInteriorRing();i++){var a=n.getInteriorRingN(i),s=t.locateInPolygonRing(e,a);if(s===yf.INTERIOR)return yf.EXTERIOR;if(s===yf.BOUNDARY)return yf.BOUNDARY}return yf.INTERIOR}if(arguments[0]instanceof ff&&arguments[1]instanceof Uh){var c=arguments[0],u=arguments[1];if(!u.getEnvelopeInternal().intersects(c))return yf.EXTERIOR;var l=u.getCoordinates();return u.isClosed()||!c.equals(l[0])&&!c.equals(l[l.length-1])?Kf.isOnLine(c,l)?yf.INTERIOR:yf.EXTERIOR:yf.BOUNDARY}if(arguments[0]instanceof ff&&arguments[1]instanceof Vh){var d=arguments[0];return arguments[1].getCoordinate().equals2D(d)?yf.INTERIOR:yf.EXTERIOR}},gy.prototype.locateInPolygonRing=function(t,e){return e.getEnvelopeInternal().intersects(t)?Kf.locatePointInRing(t,e.getCoordinates()):yf.EXTERIOR},gy.prototype.intersects=function(t,e){return this.locate(t,e)!==yf.EXTERIOR},gy.prototype.updateLocationInfo=function(t){t===yf.INTERIOR&&(this._isIn=!0),t===yf.BOUNDARY&&this._numBoundaries++},gy.prototype.computeLocation=function(t,e){var n=this;if(e instanceof Vh&&this.updateLocationInfo(this.locateInternal(t,e)),e instanceof Uh)this.updateLocationInfo(this.locateInternal(t,e));else if(e instanceof jh)this.updateLocationInfo(this.locateInternal(t,e));else if(e instanceof Oh)for(var r=e,o=0;o<r.getNumGeometries();o++){var i=r.getGeometryN(o);n.updateLocationInfo(n.locateInternal(t,i))}else if(e instanceof Wh)for(var a=e,s=0;s<a.getNumGeometries();s++){var c=a.getGeometryN(s);n.updateLocationInfo(n.locateInternal(t,c))}else if(e instanceof Dh)for(var u=new Lm(e);u.hasNext();){var l=u.next();l!==e&&n.computeLocation(t,l)}},gy.prototype.locate=function(t,e){return e.isEmpty()?yf.EXTERIOR:e instanceof Uh?this.locateInternal(t,e):e instanceof jh?this.locateInternal(t,e):(this._isIn=!1,this._numBoundaries=0,this.computeLocation(t,e),this._boundaryRule.isInBoundary(this._numBoundaries)?yf.BOUNDARY:this._numBoundaries>0||this._isIn?yf.INTERIOR:yf.EXTERIOR)},gy.prototype.interfaces_=function(){return[]},gy.prototype.getClass=function(){return gy};var my=function t(){if(this._component=null,this._segIndex=null,this._pt=null,2===arguments.length){var e=arguments[0],n=arguments[1];t.call(this,e,t.INSIDE_AREA,n)}else if(3===arguments.length){var r=arguments[0],o=arguments[1],i=arguments[2];this._component=r,this._segIndex=o,this._pt=i}},yy={INSIDE_AREA:{configurable:!0}};my.prototype.isInsideArea=function(){return this._segIndex===my.INSIDE_AREA},my.prototype.getCoordinate=function(){return this._pt},my.prototype.getGeometryComponent=function(){return this._component},my.prototype.getSegmentIndex=function(){return this._segIndex},my.prototype.interfaces_=function(){return[]},my.prototype.getClass=function(){return my},yy.INSIDE_AREA.get=function(){return-1},Object.defineProperties(my,yy);var vy=function(t){this._pts=t||null};vy.prototype.filter=function(t){t instanceof Vh&&this._pts.add(t)},vy.prototype.interfaces_=function(){return[Mh]},vy.prototype.getClass=function(){return vy},vy.getPoints=function(){if(1===arguments.length){var t=arguments[0];return t instanceof Vh?jg.singletonList(t):vy.getPoints(t,new fh)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return e instanceof Vh?n.add(e):e instanceof Dh&&e.apply(new vy(n)),n}};var _y=function(){this._locations=null;var t=arguments[0];this._locations=t};_y.prototype.filter=function(t){(t instanceof Vh||t instanceof Uh||t instanceof jh)&&this._locations.add(new my(t,0,t.getCoordinate()))},_y.prototype.interfaces_=function(){return[Mh]},_y.prototype.getClass=function(){return _y},_y.getLocations=function(t){var e=new fh;return t.apply(new _y(e)),e};var by=function(){if(this._geom=null,this._terminateDistance=0,this._ptLocator=new gy,this._minDistanceLocation=null,this._minDistance=cf.MAX_VALUE,2===arguments.length){var t=arguments[0],e=arguments[1];this._geom=[t,e],this._terminateDistance=0}else if(3===arguments.length){var n=arguments[0],r=arguments[1],o=arguments[2];this._geom=new Array(2).fill(null),this._geom[0]=n,this._geom[1]=r,this._terminateDistance=o}};by.prototype.computeContainmentDistance=function(){var t=this;if(0===arguments.length){var e=new Array(2).fill(null);if(this.computeContainmentDistance(0,e),this._minDistance<=this._terminateDistance)return null;this.computeContainmentDistance(1,e)}else if(2===arguments.length){var n=arguments[0],r=arguments[1],o=1-n,i=fy.getPolygons(this._geom[n]);if(i.size()>0){var a=_y.getLocations(this._geom[o]);if(this.computeContainmentDistance(a,i,r),this._minDistance<=this._terminateDistance)return this._minDistanceLocation[o]=r[0],this._minDistanceLocation[n]=r[1],null}}else if(3===arguments.length)if(arguments[2]instanceof Array&&_f(arguments[0],ph)&&_f(arguments[1],ph)){for(var s=arguments[0],c=arguments[1],u=arguments[2],l=0;l<s.size();l++)for(var d=s.get(l),p=0;p<c.size();p++)if(t.computeContainmentDistance(d,c.get(p),u),t._minDistance<=t._terminateDistance)return null}else if(arguments[2]instanceof Array&&arguments[0]instanceof my&&arguments[1]instanceof jh){var f=arguments[0],h=arguments[1],g=arguments[2],m=f.getCoordinate();if(yf.EXTERIOR!==this._ptLocator.locate(m,h))return this._minDistance=0,g[0]=f,g[1]=new my(h,m),null}},by.prototype.computeMinDistanceLinesPoints=function(t,e,n){for(var r=this,o=0;o<t.size();o++)for(var i=t.get(o),a=0;a<e.size();a++){var s=e.get(a);if(r.computeMinDistance(i,s,n),r._minDistance<=r._terminateDistance)return null}},by.prototype.computeFacetDistance=function(){var t=new Array(2).fill(null),e=hy.getLines(this._geom[0]),n=hy.getLines(this._geom[1]),r=vy.getPoints(this._geom[0]),o=vy.getPoints(this._geom[1]);return this.computeMinDistanceLines(e,n,t),this.updateMinDistance(t,!1),this._minDistance<=this._terminateDistance?null:(t[0]=null,t[1]=null,this.computeMinDistanceLinesPoints(e,o,t),this.updateMinDistance(t,!1),this._minDistance<=this._terminateDistance?null:(t[0]=null,t[1]=null,this.computeMinDistanceLinesPoints(n,r,t),this.updateMinDistance(t,!0),this._minDistance<=this._terminateDistance?null:(t[0]=null,t[1]=null,this.computeMinDistancePoints(r,o,t),void this.updateMinDistance(t,!1))))},by.prototype.nearestLocations=function(){return this.computeMinDistance(),this._minDistanceLocation},by.prototype.updateMinDistance=function(t,e){if(null===t[0])return null;e?(this._minDistanceLocation[0]=t[1],this._minDistanceLocation[1]=t[0]):(this._minDistanceLocation[0]=t[0],this._minDistanceLocation[1]=t[1])},by.prototype.nearestPoints=function(){return this.computeMinDistance(),[this._minDistanceLocation[0].getCoordinate(),this._minDistanceLocation[1].getCoordinate()]},by.prototype.computeMinDistance=function(){var t=this;if(0===arguments.length){if(null!==this._minDistanceLocation)return null;if(this._minDistanceLocation=new Array(2).fill(null),this.computeContainmentDistance(),this._minDistance<=this._terminateDistance)return null;this.computeFacetDistance()}else if(3===arguments.length)if(arguments[2]instanceof Array&&arguments[0]instanceof Uh&&arguments[1]instanceof Vh){var e=arguments[0],n=arguments[1],r=arguments[2];if(e.getEnvelopeInternal().distance(n.getEnvelopeInternal())>this._minDistance)return null;for(var o=e.getCoordinates(),i=n.getCoordinate(),a=0;a<o.length-1;a++){var s=Kf.distancePointLine(i,o[a],o[a+1]);if(s<t._minDistance){t._minDistance=s;var c=new im(o[a],o[a+1]),u=c.closestPoint(i);r[0]=new my(e,a,u),r[1]=new my(n,0,i)}if(t._minDistance<=t._terminateDistance)return null}}else if(arguments[2]instanceof Array&&arguments[0]instanceof Uh&&arguments[1]instanceof Uh){var l=arguments[0],d=arguments[1],p=arguments[2];if(l.getEnvelopeInternal().distance(d.getEnvelopeInternal())>this._minDistance)return null;for(var f=l.getCoordinates(),h=d.getCoordinates(),g=0;g<f.length-1;g++)for(var m=0;m<h.length-1;m++){var y=Kf.distanceLineLine(f[g],f[g+1],h[m],h[m+1]);if(y<t._minDistance){t._minDistance=y;var v=new im(f[g],f[g+1]),_=new im(h[m],h[m+1]),b=v.closestPoints(_);p[0]=new my(l,g,b[0]),p[1]=new my(d,m,b[1])}if(t._minDistance<=t._terminateDistance)return null}}},by.prototype.computeMinDistancePoints=function(t,e,n){for(var r=this,o=0;o<t.size();o++)for(var i=t.get(o),a=0;a<e.size();a++){var s=e.get(a),c=i.getCoordinate().distance(s.getCoordinate());if(c<r._minDistance&&(r._minDistance=c,n[0]=new my(i,0,i.getCoordinate()),n[1]=new my(s,0,s.getCoordinate())),r._minDistance<=r._terminateDistance)return null}},by.prototype.distance=function(){if(null===this._geom[0]||null===this._geom[1])throw new sf("null geometries are not supported");return this._geom[0].isEmpty()||this._geom[1].isEmpty()?0:(this.computeMinDistance(),this._minDistance)},by.prototype.computeMinDistanceLines=function(t,e,n){for(var r=this,o=0;o<t.size();o++)for(var i=t.get(o),a=0;a<e.size();a++){var s=e.get(a);if(r.computeMinDistance(i,s,n),r._minDistance<=r._terminateDistance)return null}},by.prototype.interfaces_=function(){return[]},by.prototype.getClass=function(){return by},by.distance=function(t,e){return new by(t,e).distance()},by.isWithinDistance=function(t,e,n){return new by(t,e,n).distance()<=n},by.nearestPoints=function(t,e){return new by(t,e).nearestPoints()};var wy=function(){this._pt=[new ff,new ff],this._distance=cf.NaN,this._isNull=!0};wy.prototype.getCoordinates=function(){return this._pt},wy.prototype.getCoordinate=function(t){return this._pt[t]},wy.prototype.setMinimum=function(){if(1===arguments.length){var t=arguments[0];this.setMinimum(t._pt[0],t._pt[1])}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(this._isNull)return this.initialize(e,n),null;var r=e.distance(n);r<this._distance&&this.initialize(e,n,r)}},wy.prototype.initialize=function(){if(0===arguments.length)this._isNull=!0;else if(2===arguments.length){var t=arguments[0],e=arguments[1];this._pt[0].setCoordinate(t),this._pt[1].setCoordinate(e),this._distance=t.distance(e),this._isNull=!1}else if(3===arguments.length){var n=arguments[0],r=arguments[1],o=arguments[2];this._pt[0].setCoordinate(n),this._pt[1].setCoordinate(r),this._distance=o,this._isNull=!1}},wy.prototype.toString=function(){return Bf.toLineString(this._pt[0],this._pt[1])},wy.prototype.getDistance=function(){return this._distance},wy.prototype.setMaximum=function(){if(1===arguments.length){var t=arguments[0];this.setMaximum(t._pt[0],t._pt[1])}else if(2===arguments.length){var e=arguments[0],n=arguments[1];if(this._isNull)return this.initialize(e,n),null;var r=e.distance(n);r>this._distance&&this.initialize(e,n,r)}},wy.prototype.interfaces_=function(){return[]},wy.prototype.getClass=function(){return wy};var xy=function(){};xy.prototype.interfaces_=function(){return[]},xy.prototype.getClass=function(){return xy},xy.computeDistance=function(){if(arguments[2]instanceof wy&&arguments[0]instanceof Uh&&arguments[1]instanceof ff)for(var t=arguments[0],e=arguments[1],n=arguments[2],r=new im,o=t.getCoordinates(),i=0;i<o.length-1;i++){r.setCoordinates(o[i],o[i+1]);var a=r.closestPoint(e);n.setMinimum(a,e)}else if(arguments[2]instanceof wy&&arguments[0]instanceof jh&&arguments[1]instanceof ff){var s=arguments[0],c=arguments[1],u=arguments[2];xy.computeDistance(s.getExteriorRing(),c,u);for(var l=0;l<s.getNumInteriorRing();l++)xy.computeDistance(s.getInteriorRingN(l),c,u)}else if(arguments[2]instanceof wy&&arguments[0]instanceof Qf&&arguments[1]instanceof ff){var d=arguments[0],p=arguments[1],f=arguments[2];if(d instanceof Uh)xy.computeDistance(d,p,f);else if(d instanceof jh)xy.computeDistance(d,p,f);else if(d instanceof Dh)for(var h=d,g=0;g<h.getNumGeometries();g++){var m=h.getGeometryN(g);xy.computeDistance(m,p,f)}else f.setMinimum(d.getCoordinate(),p)}else if(arguments[2]instanceof wy&&arguments[0]instanceof im&&arguments[1]instanceof ff){var y=arguments[0],v=arguments[1],_=arguments[2],b=y.closestPoint(v);_.setMinimum(b,v)}};var Ey=function(){this._g0=null,this._g1=null,this._ptDist=new wy,this._densifyFrac=0;var t=arguments[0],e=arguments[1];this._g0=t,this._g1=e},Cy={MaxPointDistanceFilter:{configurable:!0},MaxDensifiedByFractionDistanceFilter:{configurable:!0}};Ey.prototype.getCoordinates=function(){return this._ptDist.getCoordinates()},Ey.prototype.setDensifyFraction=function(t){if(t>1||t<=0)throw new sf("Fraction is not in range (0.0 - 1.0]");this._densifyFrac=t},Ey.prototype.compute=function(t,e){this.computeOrientedDistance(t,e,this._ptDist),this.computeOrientedDistance(e,t,this._ptDist)},Ey.prototype.distance=function(){return this.compute(this._g0,this._g1),this._ptDist.getDistance()},Ey.prototype.computeOrientedDistance=function(t,e,n){var r=new Sy(e);if(t.apply(r),n.setMaximum(r.getMaxPointDistance()),this._densifyFrac>0){var o=new Iy(e,this._densifyFrac);t.apply(o),n.setMaximum(o.getMaxPointDistance())}},Ey.prototype.orientedDistance=function(){return this.computeOrientedDistance(this._g0,this._g1,this._ptDist),this._ptDist.getDistance()},Ey.prototype.interfaces_=function(){return[]},Ey.prototype.getClass=function(){return Ey},Ey.distance=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return new Ey(t,e).distance()}if(3===arguments.length){var n=arguments[0],r=arguments[1],o=arguments[2],i=new Ey(n,r);return i.setDensifyFraction(o),i.distance()}},Cy.MaxPointDistanceFilter.get=function(){return Sy},Cy.MaxDensifiedByFractionDistanceFilter.get=function(){return Iy},Object.defineProperties(Ey,Cy);var Sy=function(){this._maxPtDist=new wy,this._minPtDist=new wy,this._euclideanDist=new xy,this._geom=null;var t=arguments[0];this._geom=t};Sy.prototype.filter=function(t){this._minPtDist.initialize(),xy.computeDistance(this._geom,t,this._minPtDist),this._maxPtDist.setMaximum(this._minPtDist)},Sy.prototype.getMaxPointDistance=function(){return this._maxPtDist},Sy.prototype.interfaces_=function(){return[nh]},Sy.prototype.getClass=function(){return Sy};var Iy=function(){this._maxPtDist=new wy,this._minPtDist=new wy,this._geom=null,this._numSubSegs=0;var t=arguments[0],e=arguments[1];this._geom=t,this._numSubSegs=Math.trunc(Math.round(1/e))};Iy.prototype.filter=function(t,e){var n=this;if(0===e)return null;for(var r=t.getCoordinate(e-1),o=t.getCoordinate(e),i=(o.x-r.x)/this._numSubSegs,a=(o.y-r.y)/this._numSubSegs,s=0;s<this._numSubSegs;s++){var c=r.x+s*i,u=r.y+s*a,l=new ff(c,u);n._minPtDist.initialize(),xy.computeDistance(n._geom,l,n._minPtDist),n._maxPtDist.setMaximum(n._minPtDist)}},Iy.prototype.isDone=function(){return!1},Iy.prototype.isGeometryChanged=function(){return!1},Iy.prototype.getMaxPointDistance=function(){return this._maxPtDist},Iy.prototype.interfaces_=function(){return[Lh]},Iy.prototype.getClass=function(){return Iy};var Py=function(t,e,n){this._minValidDistance=null,this._maxValidDistance=null,this._minDistanceFound=null,this._maxDistanceFound=null,this._isValid=!0,this._errMsg=null,this._errorLocation=null,this._errorIndicator=null,this._input=t||null,this._bufDistance=e||null,this._result=n||null},Ay={VERBOSE:{configurable:!0},MAX_DISTANCE_DIFF_FRAC:{configurable:!0}};Py.prototype.checkMaximumDistance=function(t,e,n){var r=new Ey(e,t);if(r.setDensifyFraction(.25),this._maxDistanceFound=r.orientedDistance(),this._maxDistanceFound>n){this._isValid=!1;var o=r.getCoordinates();this._errorLocation=o[1],this._errorIndicator=t.getFactory().createLineString(o),this._errMsg="Distance between buffer curve and input is too large ("+this._maxDistanceFound+" at "+Bf.toLineString(o[0],o[1])+")"}},Py.prototype.isValid=function(){var t=Math.abs(this._bufDistance),e=Py.MAX_DISTANCE_DIFF_FRAC*t;return this._minValidDistance=t-e,this._maxValidDistance=t+e,!(!this._input.isEmpty()&&!this._result.isEmpty())||(this._bufDistance>0?this.checkPositiveValid():this.checkNegativeValid(),Py.VERBOSE&&Of.out.println("Min Dist= "+this._minDistanceFound+"  err= "+(1-this._minDistanceFound/this._bufDistance)+"  Max Dist= "+this._maxDistanceFound+"  err= "+(this._maxDistanceFound/this._bufDistance-1)),this._isValid)},Py.prototype.checkNegativeValid=function(){if(!(this._input instanceof jh||this._input instanceof Wh||this._input instanceof Dh))return null;var t=this.getPolygonLines(this._input);if(this.checkMinimumDistance(t,this._result,this._minValidDistance),!this._isValid)return null;this.checkMaximumDistance(t,this._result,this._maxValidDistance)},Py.prototype.getErrorIndicator=function(){return this._errorIndicator},Py.prototype.checkMinimumDistance=function(t,e,n){var r=new by(t,e,n);if(this._minDistanceFound=r.distance(),this._minDistanceFound<n){this._isValid=!1;var o=r.nearestPoints();this._errorLocation=r.nearestPoints()[1],this._errorIndicator=t.getFactory().createLineString(o),this._errMsg="Distance between buffer curve and input is too small ("+this._minDistanceFound+" at "+Bf.toLineString(o[0],o[1])+" )"}},Py.prototype.checkPositiveValid=function(){var t=this._result.getBoundary();if(this.checkMinimumDistance(this._input,t,this._minValidDistance),!this._isValid)return null;this.checkMaximumDistance(this._input,t,this._maxValidDistance)},Py.prototype.getErrorLocation=function(){return this._errorLocation},Py.prototype.getPolygonLines=function(t){for(var e=new fh,n=new hy(e),r=fy.getPolygons(t),o=r.iterator();o.hasNext();){o.next().apply(n)}return t.getFactory().buildGeometry(e)},Py.prototype.getErrorMessage=function(){return this._errMsg},Py.prototype.interfaces_=function(){return[]},Py.prototype.getClass=function(){return Py},Ay.VERBOSE.get=function(){return!1},Ay.MAX_DISTANCE_DIFF_FRAC.get=function(){return.012},Object.defineProperties(Py,Ay);var Ny=function(t,e,n){this._isValid=!0,this._errorMsg=null,this._errorLocation=null,this._errorIndicator=null,this._input=t||null,this._distance=e||null,this._result=n||null},My={VERBOSE:{configurable:!0},MAX_ENV_DIFF_FRAC:{configurable:!0}};Ny.prototype.isValid=function(){return this.checkPolygonal(),this._isValid?(this.checkExpectedEmpty(),this._isValid?(this.checkEnvelope(),this._isValid?(this.checkArea(),this._isValid?(this.checkDistance(),this._isValid):this._isValid):this._isValid):this._isValid):this._isValid},Ny.prototype.checkEnvelope=function(){if(this._distance<0)return null;var t=this._distance*Ny.MAX_ENV_DIFF_FRAC;0===t&&(t=.001);var e=new Rf(this._input.getEnvelopeInternal());e.expandBy(this._distance);var n=new Rf(this._result.getEnvelopeInternal());n.expandBy(t),n.contains(e)||(this._isValid=!1,this._errorMsg="Buffer envelope is incorrect",this._errorIndicator=this._input.getFactory().toGeometry(n)),this.report("Envelope")},Ny.prototype.checkDistance=function(){var t=new Py(this._input,this._distance,this._result);t.isValid()||(this._isValid=!1,this._errorMsg=t.getErrorMessage(),this._errorLocation=t.getErrorLocation(),this._errorIndicator=t.getErrorIndicator()),this.report("Distance")},Ny.prototype.checkArea=function(){var t=this._input.getArea(),e=this._result.getArea();this._distance>0&&t>e&&(this._isValid=!1,this._errorMsg="Area of positive buffer is smaller than input",this._errorIndicator=this._result),this._distance<0&&t<e&&(this._isValid=!1,this._errorMsg="Area of negative buffer is larger than input",this._errorIndicator=this._result),this.report("Area")},Ny.prototype.checkPolygonal=function(){this._result instanceof jh||this._result instanceof Wh||(this._isValid=!1),this._errorMsg="Result is not polygonal",this._errorIndicator=this._result,this.report("Polygonal")},Ny.prototype.getErrorIndicator=function(){return this._errorIndicator},Ny.prototype.getErrorLocation=function(){return this._errorLocation},Ny.prototype.checkExpectedEmpty=function(){return this._input.getDimension()>=2?null:this._distance>0?null:(this._result.isEmpty()||(this._isValid=!1,this._errorMsg="Result is non-empty",this._errorIndicator=this._result),void this.report("ExpectedEmpty"))},Ny.prototype.report=function(t){if(!Ny.VERBOSE)return null;Of.out.println("Check "+t+": "+(this._isValid?"passed":"FAILED"))},Ny.prototype.getErrorMessage=function(){return this._errorMsg},Ny.prototype.interfaces_=function(){return[]},Ny.prototype.getClass=function(){return Ny},Ny.isValidMsg=function(t,e,n){var r=new Ny(t,e,n);return r.isValid()?null:r.getErrorMessage()},Ny.isValid=function(t,e,n){return!!new Ny(t,e,n).isValid()},My.VERBOSE.get=function(){return!1},My.MAX_ENV_DIFF_FRAC.get=function(){return.012},Object.defineProperties(Ny,My);var Ly=function(){this._pts=null,this._data=null;var t=arguments[0],e=arguments[1];this._pts=t,this._data=e};Ly.prototype.getCoordinates=function(){return this._pts},Ly.prototype.size=function(){return this._pts.length},Ly.prototype.getCoordinate=function(t){return this._pts[t]},Ly.prototype.isClosed=function(){return this._pts[0].equals(this._pts[this._pts.length-1])},Ly.prototype.getSegmentOctant=function(t){return t===this._pts.length-1?-1:em.octant(this.getCoordinate(t),this.getCoordinate(t+1))},Ly.prototype.setData=function(t){this._data=t},Ly.prototype.getData=function(){return this._data},Ly.prototype.toString=function(){return Bf.toLineString(new Qh(this._pts))},Ly.prototype.interfaces_=function(){return[nm]},Ly.prototype.getClass=function(){return Ly};var Dy=function(){this._findAllIntersections=!1,this._isCheckEndSegmentsOnly=!1,this._li=null,this._interiorIntersection=null,this._intSegments=null,this._intersections=new fh,this._intersectionCount=0,this._keepIntersections=!0;var t=arguments[0];this._li=t,this._interiorIntersection=null};Dy.prototype.getInteriorIntersection=function(){return this._interiorIntersection},Dy.prototype.setCheckEndSegmentsOnly=function(t){this._isCheckEndSegmentsOnly=t},Dy.prototype.getIntersectionSegments=function(){return this._intSegments},Dy.prototype.count=function(){return this._intersectionCount},Dy.prototype.getIntersections=function(){return this._intersections},Dy.prototype.setFindAllIntersections=function(t){this._findAllIntersections=t},Dy.prototype.setKeepIntersections=function(t){this._keepIntersections=t},Dy.prototype.processIntersections=function(t,e,n,r){if(!this._findAllIntersections&&this.hasIntersection())return null;if(t===n&&e===r)return null;if(this._isCheckEndSegmentsOnly){if(!(this.isEndSegment(t,e)||this.isEndSegment(n,r)))return null}var o=t.getCoordinates()[e],i=t.getCoordinates()[e+1],a=n.getCoordinates()[r],s=n.getCoordinates()[r+1];this._li.computeIntersection(o,i,a,s),this._li.hasIntersection()&&this._li.isInteriorIntersection()&&(this._intSegments=new Array(4).fill(null),this._intSegments[0]=o,this._intSegments[1]=i,this._intSegments[2]=a,this._intSegments[3]=s,this._interiorIntersection=this._li.getIntersection(0),this._keepIntersections&&this._intersections.add(this._interiorIntersection),this._intersectionCount++)},Dy.prototype.isEndSegment=function(t,e){return 0===e||e>=t.size()-2},Dy.prototype.hasIntersection=function(){return null!==this._interiorIntersection},Dy.prototype.isDone=function(){return!this._findAllIntersections&&null!==this._interiorIntersection},Dy.prototype.interfaces_=function(){return[qm]},Dy.prototype.getClass=function(){return Dy},Dy.createAllIntersectionsFinder=function(t){var e=new Dy(t);return e.setFindAllIntersections(!0),e},Dy.createAnyIntersectionFinder=function(t){return new Dy(t)},Dy.createIntersectionCounter=function(t){var e=new Dy(t);return e.setFindAllIntersections(!0),e.setKeepIntersections(!1),e};var Oy=function(){this._li=new Wf,this._segStrings=null,this._findAllIntersections=!1,this._segInt=null,this._isValid=!0;var t=arguments[0];this._segStrings=t};Oy.prototype.execute=function(){if(null!==this._segInt)return null;this.checkInteriorIntersections()},Oy.prototype.getIntersections=function(){return this._segInt.getIntersections()},Oy.prototype.isValid=function(){return this.execute(),this._isValid},Oy.prototype.setFindAllIntersections=function(t){this._findAllIntersections=t},Oy.prototype.checkInteriorIntersections=function(){this._isValid=!0,this._segInt=new Dy(this._li),this._segInt.setFindAllIntersections(this._findAllIntersections);var t=new pm;if(t.setSegmentIntersector(this._segInt),t.computeNodes(this._segStrings),this._segInt.hasIntersection())return this._isValid=!1,null},Oy.prototype.checkValid=function(){if(this.execute(),!this._isValid)throw new _g(this.getErrorMessage(),this._segInt.getInteriorIntersection())},Oy.prototype.getErrorMessage=function(){if(this._isValid)return"no intersections found";var t=this._segInt.getIntersectionSegments()
;return"found non-noded intersection between "+Bf.toLineString(t[0],t[1])+" and "+Bf.toLineString(t[2],t[3])},Oy.prototype.interfaces_=function(){return[]},Oy.prototype.getClass=function(){return Oy},Oy.computeIntersections=function(t){var e=new Oy(t);return e.setFindAllIntersections(!0),e.isValid(),e.getIntersections()};var Ty=function t(){this._nv=null;var e=arguments[0];this._nv=new Oy(t.toSegmentStrings(e))};Ty.prototype.checkValid=function(){this._nv.checkValid()},Ty.prototype.interfaces_=function(){return[]},Ty.prototype.getClass=function(){return Ty},Ty.toSegmentStrings=function(t){for(var e=new fh,n=t.iterator();n.hasNext();){var r=n.next();e.add(new Ly(r.getCoordinates(),r))}return e},Ty.checkValid=function(t){new Ty(t).checkValid()};var Ry=function(t){this._mapOp=t};Ry.prototype.map=function(t){for(var e=this,n=new fh,r=0;r<t.getNumGeometries();r++){var o=e._mapOp.map(t.getGeometryN(r));o.isEmpty()||n.add(o)}return t.getFactory().createGeometryCollection(cg.toGeometryArray(n))},Ry.prototype.interfaces_=function(){return[]},Ry.prototype.getClass=function(){return Ry},Ry.map=function(t,e){return new Ry(e).map(t)};var Fy=function(){this._op=null,this._geometryFactory=null,this._ptLocator=null,this._lineEdgesList=new fh,this._resultLineList=new fh;var t=arguments[0],e=arguments[1],n=arguments[2];this._op=t,this._geometryFactory=e,this._ptLocator=n};Fy.prototype.collectLines=function(t){for(var e=this,n=this._op.getGraph().getEdgeEnds().iterator();n.hasNext();){var r=n.next();e.collectLineEdge(r,t,e._lineEdgesList),e.collectBoundaryTouchEdge(r,t,e._lineEdgesList)}},Fy.prototype.labelIsolatedLine=function(t,e){var n=this._ptLocator.locate(t.getCoordinate(),this._op.getArgGeometry(e));t.getLabel().setLocation(e,n)},Fy.prototype.build=function(t){return this.findCoveredLineEdges(),this.collectLines(t),this.buildLines(t),this._resultLineList},Fy.prototype.collectLineEdge=function(t,e,n){var r=t.getLabel(),o=t.getEdge();t.isLineEdge()&&(t.isVisited()||!mv.isResultOfOp(r,e)||o.isCovered()||(n.add(o),t.setVisitedEdge(!0)))},Fy.prototype.findCoveredLineEdges=function(){for(var t=this,e=this._op.getGraph().getNodes().iterator();e.hasNext();){e.next().getEdges().findCoveredLineEdges()}for(var n=this._op.getGraph().getEdgeEnds().iterator();n.hasNext();){var r=n.next(),o=r.getEdge();if(r.isLineEdge()&&!o.isCoveredSet()){var i=t._op.isCoveredByA(r.getCoordinate());o.setCovered(i)}}},Fy.prototype.labelIsolatedLines=function(t){for(var e=this,n=t.iterator();n.hasNext();){var r=n.next(),o=r.getLabel();r.isIsolated()&&(o.isNull(0)?e.labelIsolatedLine(r,0):e.labelIsolatedLine(r,1))}},Fy.prototype.buildLines=function(t){for(var e=this,n=this._lineEdgesList.iterator();n.hasNext();){var r=n.next(),o=e._geometryFactory.createLineString(r.getCoordinates());e._resultLineList.add(o),r.setInResult(!0)}},Fy.prototype.collectBoundaryTouchEdge=function(t,e,n){var r=t.getLabel();return t.isLineEdge()?null:t.isVisited()?null:t.isInteriorAreaEdge()?null:t.getEdge().isInResult()?null:(jf.isTrue(!(t.isInResult()||t.getSym().isInResult())||!t.getEdge().isInResult()),void(mv.isResultOfOp(r,e)&&e===mv.INTERSECTION&&(n.add(t.getEdge()),t.setVisitedEdge(!0))))},Fy.prototype.interfaces_=function(){return[]},Fy.prototype.getClass=function(){return Fy};var Gy=function(){this._op=null,this._geometryFactory=null,this._resultPointList=new fh;var t=arguments[0],e=arguments[1];this._op=t,this._geometryFactory=e};Gy.prototype.filterCoveredNodeToPoint=function(t){var e=t.getCoordinate();if(!this._op.isCoveredByLA(e)){var n=this._geometryFactory.createPoint(e);this._resultPointList.add(n)}},Gy.prototype.extractNonCoveredResultNodes=function(t){for(var e=this,n=this._op.getGraph().getNodes().iterator();n.hasNext();){var r=n.next();if(!r.isInResult()&&(!r.isIncidentEdgeInResult()&&(0===r.getEdges().getDegree()||t===mv.INTERSECTION))){var o=r.getLabel();mv.isResultOfOp(o,t)&&e.filterCoveredNodeToPoint(r)}}},Gy.prototype.build=function(t){return this.extractNonCoveredResultNodes(t),this._resultPointList},Gy.prototype.interfaces_=function(){return[]},Gy.prototype.getClass=function(){return Gy};var qy=function(){this._inputGeom=null,this._factory=null,this._pruneEmptyGeometry=!0,this._preserveGeometryCollectionType=!0,this._preserveCollections=!1,this._preserveType=!1};qy.prototype.transformPoint=function(t,e){return this._factory.createPoint(this.transformCoordinates(t.getCoordinateSequence(),t))},qy.prototype.transformPolygon=function(t,e){var n=this,r=!0,o=this.transformLinearRing(t.getExteriorRing(),t);null!==o&&o instanceof Xh&&!o.isEmpty()||(r=!1);for(var i=new fh,a=0;a<t.getNumInteriorRing();a++){var s=n.transformLinearRing(t.getInteriorRingN(a),t);null===s||s.isEmpty()||(s instanceof Xh||(r=!1),i.add(s))}if(r)return this._factory.createPolygon(o,i.toArray([]));var c=new fh;return null!==o&&c.add(o),c.addAll(i),this._factory.buildGeometry(c)},qy.prototype.createCoordinateSequence=function(t){return this._factory.getCoordinateSequenceFactory().create(t)},qy.prototype.getInputGeometry=function(){return this._inputGeom},qy.prototype.transformMultiLineString=function(t,e){for(var n=this,r=new fh,o=0;o<t.getNumGeometries();o++){var i=n.transformLineString(t.getGeometryN(o),t);null!==i&&(i.isEmpty()||r.add(i))}return this._factory.buildGeometry(r)},qy.prototype.transformCoordinates=function(t,e){return this.copy(t)},qy.prototype.transformLineString=function(t,e){return this._factory.createLineString(this.transformCoordinates(t.getCoordinateSequence(),t))},qy.prototype.transformMultiPoint=function(t,e){for(var n=this,r=new fh,o=0;o<t.getNumGeometries();o++){var i=n.transformPoint(t.getGeometryN(o),t);null!==i&&(i.isEmpty()||r.add(i))}return this._factory.buildGeometry(r)},qy.prototype.transformMultiPolygon=function(t,e){for(var n=this,r=new fh,o=0;o<t.getNumGeometries();o++){var i=n.transformPolygon(t.getGeometryN(o),t);null!==i&&(i.isEmpty()||r.add(i))}return this._factory.buildGeometry(r)},qy.prototype.copy=function(t){return t.copy()},qy.prototype.transformGeometryCollection=function(t,e){for(var n=this,r=new fh,o=0;o<t.getNumGeometries();o++){var i=n.transform(t.getGeometryN(o));null!==i&&(n._pruneEmptyGeometry&&i.isEmpty()||r.add(i))}return this._preserveGeometryCollectionType?this._factory.createGeometryCollection(cg.toGeometryArray(r)):this._factory.buildGeometry(r)},qy.prototype.transform=function(t){if(this._inputGeom=t,this._factory=t.getFactory(),t instanceof Vh)return this.transformPoint(t,null);if(t instanceof Yh)return this.transformMultiPoint(t,null);if(t instanceof Xh)return this.transformLinearRing(t,null);if(t instanceof Uh)return this.transformLineString(t,null);if(t instanceof Oh)return this.transformMultiLineString(t,null);if(t instanceof jh)return this.transformPolygon(t,null);if(t instanceof Wh)return this.transformMultiPolygon(t,null);if(t instanceof Dh)return this.transformGeometryCollection(t,null);throw new sf("Unknown Geometry subtype: "+t.getClass().getName())},qy.prototype.transformLinearRing=function(t,e){var n=this.transformCoordinates(t.getCoordinateSequence(),t);if(null===n)return this._factory.createLinearRing(null);var r=n.size();return r>0&&r<4&&!this._preserveType?this._factory.createLineString(n):this._factory.createLinearRing(n)},qy.prototype.interfaces_=function(){return[]},qy.prototype.getClass=function(){return qy};var ky=function t(){if(this._snapTolerance=0,this._srcPts=null,this._seg=new im,this._allowSnappingToSourceVertices=!1,this._isClosed=!1,arguments[0]instanceof Uh&&"number"==typeof arguments[1]){var e=arguments[0],n=arguments[1];t.call(this,e.getCoordinates(),n)}else if(arguments[0]instanceof Array&&"number"==typeof arguments[1]){var r=arguments[0],o=arguments[1];this._srcPts=r,this._isClosed=t.isClosed(r),this._snapTolerance=o}};ky.prototype.snapVertices=function(t,e){for(var n=this,r=this._isClosed?t.size()-1:t.size(),o=0;o<r;o++){var i=t.get(o),a=n.findSnapForVertex(i,e);null!==a&&(t.set(o,new ff(a)),0===o&&n._isClosed&&t.set(t.size()-1,new ff(a)))}},ky.prototype.findSnapForVertex=function(t,e){for(var n=this,r=0;r<e.length;r++){if(t.equals2D(e[r]))return null;if(t.distance(e[r])<n._snapTolerance)return e[r]}return null},ky.prototype.snapTo=function(t){var e=new gh(this._srcPts);return this.snapVertices(e,t),this.snapSegments(e,t),e.toCoordinateArray()},ky.prototype.snapSegments=function(t,e){var n=this;if(0===e.length)return null;var r=e.length;e[0].equals2D(e[e.length-1])&&(r=e.length-1);for(var o=0;o<r;o++){var i=e[o],a=n.findSegmentIndexToSnap(i,t);a>=0&&t.add(a+1,new ff(i),!1)}},ky.prototype.findSegmentIndexToSnap=function(t,e){for(var n=this,r=cf.MAX_VALUE,o=-1,i=0;i<e.size()-1;i++){if(n._seg.p0=e.get(i),n._seg.p1=e.get(i+1),n._seg.p0.equals2D(t)||n._seg.p1.equals2D(t)){if(n._allowSnappingToSourceVertices)continue;return-1}var a=n._seg.distance(t);a<n._snapTolerance&&a<r&&(r=a,o=i)}return o},ky.prototype.setAllowSnappingToSourceVertices=function(t){this._allowSnappingToSourceVertices=t},ky.prototype.interfaces_=function(){return[]},ky.prototype.getClass=function(){return ky},ky.isClosed=function(t){return!(t.length<=1)&&t[0].equals2D(t[t.length-1])};var Uy=function(t){this._srcGeom=t||null},By={SNAP_PRECISION_FACTOR:{configurable:!0}};Uy.prototype.snapTo=function(t,e){var n=this.extractTargetCoordinates(t);return new Vy(e,n).transform(this._srcGeom)},Uy.prototype.snapToSelf=function(t,e){var n=this.extractTargetCoordinates(this._srcGeom),r=new Vy(t,n,!0),o=r.transform(this._srcGeom),i=o;return e&&_f(i,zh)&&(i=o.buffer(0)),i},Uy.prototype.computeSnapTolerance=function(t){return this.computeMinimumSegmentLength(t)/10},Uy.prototype.extractTargetCoordinates=function(t){for(var e=new mc,n=t.getCoordinates(),r=0;r<n.length;r++)e.add(n[r]);return e.toArray(new Array(0).fill(null))},Uy.prototype.computeMinimumSegmentLength=function(t){for(var e=cf.MAX_VALUE,n=0;n<t.length-1;n++){var r=t[n].distance(t[n+1]);r<e&&(e=r)}return e},Uy.prototype.interfaces_=function(){return[]},Uy.prototype.getClass=function(){return Uy},Uy.snap=function(t,e,n){var r=new Array(2).fill(null),o=new Uy(t);r[0]=o.snapTo(e,n);var i=new Uy(e);return r[1]=i.snapTo(r[0],n),r},Uy.computeOverlaySnapTolerance=function(){if(1===arguments.length){var t=arguments[0],e=Uy.computeSizeBasedSnapTolerance(t),n=t.getPrecisionModel();if(n.getType()===og.FIXED){var r=1/n.getScale()*2/1.415;r>e&&(e=r)}return e}if(2===arguments.length){var o=arguments[0],i=arguments[1];return Math.min(Uy.computeOverlaySnapTolerance(o),Uy.computeOverlaySnapTolerance(i))}},Uy.computeSizeBasedSnapTolerance=function(t){var e=t.getEnvelopeInternal();return Math.min(e.getHeight(),e.getWidth())*Uy.SNAP_PRECISION_FACTOR},Uy.snapToSelf=function(t,e,n){return new Uy(t).snapToSelf(e,n)},By.SNAP_PRECISION_FACTOR.get=function(){return 1e-9},Object.defineProperties(Uy,By);var Vy=function(t){function e(e,n,r){t.call(this),this._snapTolerance=e||null,this._snapPts=n||null,this._isSelfSnap=void 0!==r&&r}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.snapLine=function(t,e){var n=new ky(t,this._snapTolerance);return n.setAllowSnappingToSourceVertices(this._isSelfSnap),n.snapTo(e)},e.prototype.transformCoordinates=function(t,e){var n=t.toCoordinateArray(),r=this.snapLine(n,this._snapPts);return this._factory.getCoordinateSequenceFactory().create(r)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(qy),zy=function(){this._isFirst=!0,this._commonMantissaBitsCount=53,this._commonBits=0,this._commonSignExp=null};zy.prototype.getCommon=function(){return cf.longBitsToDouble(this._commonBits)},zy.prototype.add=function(t){var e=cf.doubleToLongBits(t);return this._isFirst?(this._commonBits=e,this._commonSignExp=zy.signExpBits(this._commonBits),this._isFirst=!1,null):zy.signExpBits(e)!==this._commonSignExp?(this._commonBits=0,null):(this._commonMantissaBitsCount=zy.numCommonMostSigMantissaBits(this._commonBits,e),void(this._commonBits=zy.zeroLowerBits(this._commonBits,64-(12+this._commonMantissaBitsCount))))},zy.prototype.toString=function(){if(1===arguments.length){var t=arguments[0],e=cf.longBitsToDouble(t),n=cf.toBinaryString(t),r="0000000000000000000000000000000000000000000000000000000000000000"+n,o=r.substring(r.length-64);return o.substring(0,1)+"  "+o.substring(1,12)+"(exp) "+o.substring(12)+" [ "+e+" ]"}},zy.prototype.interfaces_=function(){return[]},zy.prototype.getClass=function(){return zy},zy.getBit=function(t,e){return 0!=(t&1<<e)?1:0},zy.signExpBits=function(t){return t>>52},zy.zeroLowerBits=function(t,e){return t&~((1<<e)-1)},zy.numCommonMostSigMantissaBits=function(t,e){for(var n=0,r=52;r>=0;r--){if(zy.getBit(t,r)!==zy.getBit(e,r))return n;n++}return 52};var jy=function(){this._commonCoord=null,this._ccFilter=new Xy},Yy={CommonCoordinateFilter:{configurable:!0},Translater:{configurable:!0}};jy.prototype.addCommonBits=function(t){var e=new Wy(this._commonCoord);t.apply(e),t.geometryChanged()},jy.prototype.removeCommonBits=function(t){if(0===this._commonCoord.x&&0===this._commonCoord.y)return t;var e=new ff(this._commonCoord);e.x=-e.x,e.y=-e.y;var n=new Wy(e);return t.apply(n),t.geometryChanged(),t},jy.prototype.getCommonCoordinate=function(){return this._commonCoord},jy.prototype.add=function(t){t.apply(this._ccFilter),this._commonCoord=this._ccFilter.getCommonCoordinate()},jy.prototype.interfaces_=function(){return[]},jy.prototype.getClass=function(){return jy},Yy.CommonCoordinateFilter.get=function(){return Xy},Yy.Translater.get=function(){return Wy},Object.defineProperties(jy,Yy);var Xy=function(){this._commonBitsX=new zy,this._commonBitsY=new zy};Xy.prototype.filter=function(t){this._commonBitsX.add(t.x),this._commonBitsY.add(t.y)},Xy.prototype.getCommonCoordinate=function(){return new ff(this._commonBitsX.getCommon(),this._commonBitsY.getCommon())},Xy.prototype.interfaces_=function(){return[nh]},Xy.prototype.getClass=function(){return Xy};var Wy=function(){this.trans=null;var t=arguments[0];this.trans=t};Wy.prototype.filter=function(t,e){var n=t.getOrdinate(e,0)+this.trans.x,r=t.getOrdinate(e,1)+this.trans.y;t.setOrdinate(e,0,n),t.setOrdinate(e,1,r)},Wy.prototype.isDone=function(){return!1},Wy.prototype.isGeometryChanged=function(){return!0},Wy.prototype.interfaces_=function(){return[Lh]},Wy.prototype.getClass=function(){return Wy};var $y=function(t,e){this._geom=new Array(2).fill(null),this._snapTolerance=null,this._cbr=null,this._geom[0]=t,this._geom[1]=e,this.computeSnapTolerance()};$y.prototype.selfSnap=function(t){return new Uy(t).snapTo(t,this._snapTolerance)},$y.prototype.removeCommonBits=function(t){this._cbr=new jy,this._cbr.add(t[0]),this._cbr.add(t[1]);var e=new Array(2).fill(null);return e[0]=this._cbr.removeCommonBits(t[0].copy()),e[1]=this._cbr.removeCommonBits(t[1].copy()),e},$y.prototype.prepareResult=function(t){return this._cbr.addCommonBits(t),t},$y.prototype.getResultGeometry=function(t){var e=this.snap(this._geom),n=mv.overlayOp(e[0],e[1],t);return this.prepareResult(n)},$y.prototype.checkValid=function(t){t.isValid()||Of.out.println("Snapped geometry is invalid")},$y.prototype.computeSnapTolerance=function(){this._snapTolerance=Uy.computeOverlaySnapTolerance(this._geom[0],this._geom[1])},$y.prototype.snap=function(t){var e=this.removeCommonBits(t);return Uy.snap(e[0],e[1],this._snapTolerance)},$y.prototype.interfaces_=function(){return[]},$y.prototype.getClass=function(){return $y},$y.overlayOp=function(t,e,n){return new $y(t,e).getResultGeometry(n)},$y.union=function(t,e){return $y.overlayOp(t,e,mv.UNION)},$y.intersection=function(t,e){return $y.overlayOp(t,e,mv.INTERSECTION)},$y.symDifference=function(t,e){return $y.overlayOp(t,e,mv.SYMDIFFERENCE)},$y.difference=function(t,e){return $y.overlayOp(t,e,mv.DIFFERENCE)};var Hy=function(t,e){this._geom=new Array(2).fill(null),this._geom[0]=t,this._geom[1]=e};Hy.prototype.getResultGeometry=function(t){var e=null,n=!1,r=null;try{e=mv.overlayOp(this._geom[0],this._geom[1],t);n=!0}catch(t){if(!(t instanceof Vf))throw t;r=t}if(!n)try{e=$y.overlayOp(this._geom[0],this._geom[1],t)}catch(t){throw t instanceof Vf?r:t}return e},Hy.prototype.interfaces_=function(){return[]},Hy.prototype.getClass=function(){return Hy},Hy.overlayOp=function(t,e,n){return new Hy(t,e).getResultGeometry(n)},Hy.union=function(t,e){return Hy.overlayOp(t,e,mv.UNION)},Hy.intersection=function(t,e){return Hy.overlayOp(t,e,mv.INTERSECTION)},Hy.symDifference=function(t,e){return Hy.overlayOp(t,e,mv.SYMDIFFERENCE)},Hy.difference=function(t,e){return Hy.overlayOp(t,e,mv.DIFFERENCE)};var Ky=function(){this.mce=null,this.chainIndex=null;var t=arguments[0],e=arguments[1];this.mce=t,this.chainIndex=e};Ky.prototype.computeIntersections=function(t,e){this.mce.computeIntersectsForChain(this.chainIndex,t.mce,t.chainIndex,e)},Ky.prototype.interfaces_=function(){return[]},Ky.prototype.getClass=function(){return Ky};var Jy=function t(){if(this._label=null,this._xValue=null,this._eventType=null,this._insertEvent=null,this._deleteEventIndex=null,this._obj=null,2===arguments.length){var e=arguments[0],n=arguments[1];this._eventType=t.DELETE,this._xValue=e,this._insertEvent=n}else if(3===arguments.length){var r=arguments[0],o=arguments[1],i=arguments[2];this._eventType=t.INSERT,this._label=r,this._xValue=o,this._obj=i}},Zy={INSERT:{configurable:!0},DELETE:{configurable:!0}};Jy.prototype.isDelete=function(){return this._eventType===Jy.DELETE},Jy.prototype.setDeleteEventIndex=function(t){this._deleteEventIndex=t},Jy.prototype.getObject=function(){return this._obj},Jy.prototype.compareTo=function(t){var e=t;return this._xValue<e._xValue?-1:this._xValue>e._xValue?1:this._eventType<e._eventType?-1:this._eventType>e._eventType?1:0},Jy.prototype.getInsertEvent=function(){return this._insertEvent},Jy.prototype.isInsert=function(){return this._eventType===Jy.INSERT},Jy.prototype.isSameLabel=function(t){return null!==this._label&&this._label===t._label},Jy.prototype.getDeleteEventIndex=function(){return this._deleteEventIndex},Jy.prototype.interfaces_=function(){return[lf]},Jy.prototype.getClass=function(){return Jy},Zy.INSERT.get=function(){return 1},Zy.DELETE.get=function(){return 2},Object.defineProperties(Jy,Zy);var Qy=function(){};Qy.prototype.interfaces_=function(){return[]},Qy.prototype.getClass=function(){return Qy};var tv=function(){this._hasIntersection=!1,this._hasProper=!1,this._hasProperInterior=!1,this._properIntersectionPoint=null,this._li=null,this._includeProper=null,this._recordIsolated=null,this._isSelfIntersection=null,this._numIntersections=0,this.numTests=0,this._bdyNodes=null,this._isDone=!1,this._isDoneWhenProperInt=!1;var t=arguments[0],e=arguments[1],n=arguments[2];this._li=t,this._includeProper=e,this._recordIsolated=n};tv.prototype.isTrivialIntersection=function(t,e,n,r){if(t===n&&1===this._li.getIntersectionNum()){if(tv.isAdjacentSegments(e,r))return!0;if(t.isClosed()){var o=t.getNumPoints()-1;if(0===e&&r===o||0===r&&e===o)return!0}}return!1},tv.prototype.getProperIntersectionPoint=function(){return this._properIntersectionPoint},tv.prototype.setIsDoneIfProperInt=function(t){this._isDoneWhenProperInt=t},tv.prototype.hasProperInteriorIntersection=function(){return this._hasProperInterior},tv.prototype.isBoundaryPointInternal=function(t,e){for(var n=e.iterator();n.hasNext();){var r=n.next(),o=r.getCoordinate();if(t.isIntersection(o))return!0}return!1},tv.prototype.hasProperIntersection=function(){return this._hasProper},tv.prototype.hasIntersection=function(){return this._hasIntersection},tv.prototype.isDone=function(){return this._isDone},tv.prototype.isBoundaryPoint=function(t,e){return null!==e&&(!!this.isBoundaryPointInternal(t,e[0])||!!this.isBoundaryPointInternal(t,e[1]))},tv.prototype.setBoundaryNodes=function(t,e){this._bdyNodes=new Array(2).fill(null),this._bdyNodes[0]=t,this._bdyNodes[1]=e},tv.prototype.addIntersections=function(t,e,n,r){if(t===n&&e===r)return null;this.numTests++;var o=t.getCoordinates()[e],i=t.getCoordinates()[e+1],a=n.getCoordinates()[r],s=n.getCoordinates()[r+1];this._li.computeIntersection(o,i,a,s),this._li.hasIntersection()&&(this._recordIsolated&&(t.setIsolated(!1),n.setIsolated(!1)),this._numIntersections++,this.isTrivialIntersection(t,e,n,r)||(this._hasIntersection=!0,!this._includeProper&&this._li.isProper()||(t.addIntersections(this._li,e,0),n.addIntersections(this._li,r,1)),this._li.isProper()&&(this._properIntersectionPoint=this._li.getIntersection(0).copy(),this._hasProper=!0,this._isDoneWhenProperInt&&(this._isDone=!0),this.isBoundaryPoint(this._li,this._bdyNodes)||(this._hasProperInterior=!0))))},tv.prototype.interfaces_=function(){return[]},tv.prototype.getClass=function(){return tv},tv.isAdjacentSegments=function(t,e){return 1===Math.abs(t-e)};var ev=function(t){function e(){t.call(this),this.events=new fh,this.nOverlaps=null}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.prepareEvents=function(){var t=this;jg.sort(this.events);for(var e=0;e<this.events.size();e++){var n=t.events.get(e);n.isDelete()&&n.getInsertEvent().setDeleteEventIndex(e)}},e.prototype.computeIntersections=function(){var t=this;if(1===arguments.length){var e=arguments[0];this.nOverlaps=0,this.prepareEvents();for(var n=0;n<this.events.size();n++){var r=t.events.get(n);if(r.isInsert()&&t.processOverlaps(n,r.getDeleteEventIndex(),r,e),e.isDone())break}}else if(3===arguments.length)if(arguments[2]instanceof tv&&_f(arguments[0],ph)&&_f(arguments[1],ph)){var o=arguments[0],i=arguments[1],a=arguments[2];this.addEdges(o,o),this.addEdges(i,i),this.computeIntersections(a)}else if("boolean"==typeof arguments[2]&&_f(arguments[0],ph)&&arguments[1]instanceof tv){var s=arguments[0],c=arguments[1],u=arguments[2];u?this.addEdges(s,null):this.addEdges(s),this.computeIntersections(c)}},e.prototype.addEdge=function(t,e){for(var n=this,r=t.getMonotoneChainEdge(),o=r.getStartIndexes(),i=0;i<o.length-1;i++){var a=new Ky(r,i),s=new Jy(e,r.getMinX(i),a);n.events.add(s),n.events.add(new Jy(r.getMaxX(i),s))}},e.prototype.processOverlaps=function(t,e,n,r){for(var o=this,i=n.getObject(),a=t;a<e;a++){var s=o.events.get(a);if(s.isInsert()){var c=s.getObject();n.isSameLabel(s)||(i.computeIntersections(c,r),o.nOverlaps++)}}},e.prototype.addEdges=function(){var t=this;if(1===arguments.length)for(var e=arguments[0],n=e.iterator();n.hasNext();){var r=n.next();t.addEdge(r,r)}else if(2===arguments.length)for(var o=arguments[0],i=arguments[1],a=o.iterator();a.hasNext();){var s=a.next();t.addEdge(s,i)}},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(Qy),nv=function(){this._min=cf.POSITIVE_INFINITY,this._max=cf.NEGATIVE_INFINITY},rv={NodeComparator:{configurable:!0}};nv.prototype.getMin=function(){return this._min},nv.prototype.intersects=function(t,e){return!(this._min>e||this._max<t)},nv.prototype.getMax=function(){return this._max},nv.prototype.toString=function(){return Bf.toLineString(new ff(this._min,0),new ff(this._max,0))},nv.prototype.interfaces_=function(){return[]},nv.prototype.getClass=function(){return nv},rv.NodeComparator.get=function(){return ov},Object.defineProperties(nv,rv);var ov=function(){};ov.prototype.compare=function(t,e){var n=t,r=e,o=(n._min+n._max)/2,i=(r._min+r._max)/2;return o<i?-1:o>i?1:0},ov.prototype.interfaces_=function(){return[pf]},ov.prototype.getClass=function(){return ov};var iv=function(t){function e(){t.call(this),this._item=null;var e=arguments[0],n=arguments[1],r=arguments[2];this._min=e,this._max=n,this._item=r}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.query=function(t,e,n){if(!this.intersects(t,e))return null;n.visitItem(this._item)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(nv),av=function(t){function e(){t.call(this),this._node1=null,this._node2=null;var e=arguments[0],n=arguments[1];this._node1=e,this._node2=n,this.buildExtent(this._node1,this._node2)}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.buildExtent=function(t,e){this._min=Math.min(t._min,e._min),this._max=Math.max(t._max,e._max)},e.prototype.query=function(t,e,n){if(!this.intersects(t,e))return null;null!==this._node1&&this._node1.query(t,e,n),null!==this._node2&&this._node2.query(t,e,n)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(nv),sv=function(){this._leaves=new fh,this._root=null,this._level=0};sv.prototype.buildTree=function(){var t=this;jg.sort(this._leaves,new nv.NodeComparator);for(var e=this._leaves,n=null,r=new fh;;){if(t.buildLevel(e,r),1===r.size())return r.get(0);n=e,e=r,r=n}},sv.prototype.insert=function(t,e,n){if(null!==this._root)throw new Error("Index cannot be added to once it has been queried");this._leaves.add(new iv(t,e,n))},sv.prototype.query=function(t,e,n){this.init(),this._root.query(t,e,n)},sv.prototype.buildRoot=function(){if(null!==this._root)return null;this._root=this.buildTree()},sv.prototype.printNode=function(t){Of.out.println(Bf.toLineString(new ff(t._min,this._level),new ff(t._max,this._level)))},sv.prototype.init=function(){if(null!==this._root)return null;this.buildRoot()},sv.prototype.buildLevel=function(t,e){this._level++,e.clear();for(var n=0;n<t.size();n+=2){var r=t.get(n);if(null===(n+1<t.size()?t.get(n):null))e.add(r);else{var o=new av(t.get(n),t.get(n+1));e.add(o)}}},sv.prototype.interfaces_=function(){return[]},sv.prototype.getClass=function(){return sv};var cv=function(){this._items=new fh};cv.prototype.visitItem=function(t){this._items.add(t)},cv.prototype.getItems=function(){return this._items},cv.prototype.interfaces_=function(){return[Ug]},cv.prototype.getClass=function(){return cv};var uv=function(){this._index=null;var t=arguments[0];if(!_f(t,zh))throw new sf("Argument must be Polygonal");this._index=new pv(t)},lv={SegmentVisitor:{configurable:!0},IntervalIndexedGeometry:{configurable:!0}};uv.prototype.locate=function(t){var e=new Hf(t),n=new dv(e);return this._index.query(t.y,t.y,n),e.getLocation()},uv.prototype.interfaces_=function(){return[Mm]},uv.prototype.getClass=function(){return uv},lv.SegmentVisitor.get=function(){return dv},lv.IntervalIndexedGeometry.get=function(){return pv},Object.defineProperties(uv,lv);var dv=function(){this._counter=null;var t=arguments[0];this._counter=t};dv.prototype.visitItem=function(t){var e=t;this._counter.countSegment(e.getCoordinate(0),e.getCoordinate(1))},dv.prototype.interfaces_=function(){return[Ug]},dv.prototype.getClass=function(){return dv};var pv=function(){this._index=new sv;var t=arguments[0];this.init(t)};pv.prototype.init=function(t){for(var e=this,n=hy.getLines(t),r=n.iterator();r.hasNext();){var o=r.next(),i=o.getCoordinates();e.addLine(i)}},pv.prototype.addLine=function(t){for(var e=this,n=1;n<t.length;n++){var r=new im(t[n-1],t[n]),o=Math.min(r.p0.y,r.p1.y),i=Math.max(r.p0.y,r.p1.y);e._index.insert(o,i,r)}},pv.prototype.query=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=new cv;return this._index.query(t,e,n),n.getItems()}if(3===arguments.length){var r=arguments[0],o=arguments[1],i=arguments[2];this._index.query(r,o,i)}},pv.prototype.interfaces_=function(){return[]},pv.prototype.getClass=function(){return pv};var fv=function(t){function e(){if(t.call(this),this._parentGeom=null,this._lineEdgeMap=new rg,this._boundaryNodeRule=null,this._useBoundaryDeterminationRule=!0,this._argIndex=null,this._boundaryNodes=null,this._hasTooFewPoints=!1,this._invalidPoint=null,this._areaPtLocator=null,this._ptLocator=new gy,2===arguments.length){var e=arguments[0],n=arguments[1],r=rh.OGC_SFS_BOUNDARY_RULE;this._argIndex=e,this._parentGeom=n,this._boundaryNodeRule=r,null!==n&&this.add(n)}else if(3===arguments.length){var o=arguments[0],i=arguments[1],a=arguments[2];this._argIndex=o,this._parentGeom=i,this._boundaryNodeRule=a,null!==i&&this.add(i)}}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.insertBoundaryPoint=function(t,n){var r=this._nodes.addNode(n),o=r.getLabel(),i=1,a=yf.NONE;(a=o.getLocation(t,mg.ON))===yf.BOUNDARY&&i++;var s=e.determineBoundary(this._boundaryNodeRule,i);o.setLocation(t,s)},e.prototype.computeSelfNodes=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1];return this.computeSelfNodes(t,e,!1)}if(3===arguments.length){var n=arguments[0],r=arguments[1],o=arguments[2],i=new tv(n,!0,!1);i.setIsDoneIfProperInt(o);var a=this.createEdgeSetIntersector(),s=this._parentGeom instanceof Xh||this._parentGeom instanceof jh||this._parentGeom instanceof Wh,c=r||!s;return a.computeIntersections(this._edges,i,c),this.addSelfIntersectionNodes(this._argIndex),i}},e.prototype.computeSplitEdges=function(t){for(var e=this._edges.iterator();e.hasNext();){e.next().eiList.addSplitEdges(t)}},e.prototype.computeEdgeIntersections=function(t,e,n){var r=new tv(e,n,!0);return r.setBoundaryNodes(this.getBoundaryNodes(),t.getBoundaryNodes()),this.createEdgeSetIntersector().computeIntersections(this._edges,t._edges,r),r},e.prototype.getGeometry=function(){return this._parentGeom},e.prototype.getBoundaryNodeRule=function(){return this._boundaryNodeRule},e.prototype.hasTooFewPoints=function(){return this._hasTooFewPoints},e.prototype.addPoint=function(){if(arguments[0]instanceof Vh){var t=arguments[0],e=t.getCoordinate();this.insertPoint(this._argIndex,e,yf.INTERIOR)}else if(arguments[0]instanceof ff){var n=arguments[0];this.insertPoint(this._argIndex,n,yf.INTERIOR)}},e.prototype.addPolygon=function(t){var e=this;this.addPolygonRing(t.getExteriorRing(),yf.EXTERIOR,yf.INTERIOR);for(var n=0;n<t.getNumInteriorRing();n++){var r=t.getInteriorRingN(n);e.addPolygonRing(r,yf.INTERIOR,yf.EXTERIOR)}},e.prototype.addEdge=function(t){this.insertEdge(t);var e=t.getCoordinates();this.insertPoint(this._argIndex,e[0],yf.BOUNDARY),this.insertPoint(this._argIndex,e[e.length-1],yf.BOUNDARY)},e.prototype.addLineString=function(t){var e=mh.removeRepeatedPoints(t.getCoordinates());if(e.length<2)return this._hasTooFewPoints=!0,this._invalidPoint=e[0],null;var n=new Xm(e,new Eg(this._argIndex,yf.INTERIOR));this._lineEdgeMap.put(t,n),this.insertEdge(n),jf.isTrue(e.length>=2,"found LineString with single point"),this.insertBoundaryPoint(this._argIndex,e[0]),this.insertBoundaryPoint(this._argIndex,e[e.length-1])},e.prototype.getInvalidPoint=function(){return this._invalidPoint},e.prototype.getBoundaryPoints=function(){for(var t=this.getBoundaryNodes(),e=new Array(t.size()).fill(null),n=0,r=t.iterator();r.hasNext();){var o=r.next();e[n++]=o.getCoordinate().copy()}return e},e.prototype.getBoundaryNodes=function(){return null===this._boundaryNodes&&(this._boundaryNodes=this._nodes.getBoundaryNodes(this._argIndex)),this._boundaryNodes},e.prototype.addSelfIntersectionNode=function(t,e,n){if(this.isBoundaryNode(t,e))return null;n===yf.BOUNDARY&&this._useBoundaryDeterminationRule?this.insertBoundaryPoint(t,e):this.insertPoint(t,e,n)},e.prototype.addPolygonRing=function(t,e,n){if(t.isEmpty())return null;var r=mh.removeRepeatedPoints(t.getCoordinates());if(r.length<4)return this._hasTooFewPoints=!0,this._invalidPoint=r[0],null;var o=e,i=n;Kf.isCCW(r)&&(o=n,i=e);var a=new Xm(r,new Eg(this._argIndex,yf.BOUNDARY,o,i));this._lineEdgeMap.put(t,a),this.insertEdge(a),this.insertPoint(this._argIndex,r[0],yf.BOUNDARY)},e.prototype.insertPoint=function(t,e,n){var r=this._nodes.addNode(e),o=r.getLabel();null===o?r._label=new Eg(t,n):o.setLocation(t,n)},e.prototype.createEdgeSetIntersector=function(){return new ev},e.prototype.addSelfIntersectionNodes=function(t){for(var e=this,n=this._edges.iterator();n.hasNext();)for(var r=n.next(),o=r.getLabel().getLocation(t),i=r.eiList.iterator();i.hasNext();){var a=i.next();e.addSelfIntersectionNode(t,a.coord,o)}},e.prototype.add=function(){if(1!==arguments.length)return t.prototype.add.apply(this,arguments);var e=arguments[0];if(e.isEmpty())return null
;if(e instanceof Wh&&(this._useBoundaryDeterminationRule=!1),e instanceof jh)this.addPolygon(e);else if(e instanceof Uh)this.addLineString(e);else if(e instanceof Vh)this.addPoint(e);else if(e instanceof Yh)this.addCollection(e);else if(e instanceof Oh)this.addCollection(e);else if(e instanceof Wh)this.addCollection(e);else{if(!(e instanceof Dh))throw new Error(e.getClass().getName());this.addCollection(e)}},e.prototype.addCollection=function(t){for(var e=this,n=0;n<t.getNumGeometries();n++){var r=t.getGeometryN(n);e.add(r)}},e.prototype.locate=function(t){return _f(this._parentGeom,zh)&&this._parentGeom.getNumGeometries()>50?(null===this._areaPtLocator&&(this._areaPtLocator=new uv(this._parentGeom)),this._areaPtLocator.locate(t)):this._ptLocator.locate(t,this._parentGeom)},e.prototype.findEdge=function(){if(1===arguments.length){var e=arguments[0];return this._lineEdgeMap.get(e)}return t.prototype.findEdge.apply(this,arguments)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e.determineBoundary=function(t,e){return t.isInBoundary(e)?yf.BOUNDARY:yf.INTERIOR},e}(Rg),hv=function(){if(this._li=new Wf,this._resultPrecisionModel=null,this._arg=null,1===arguments.length){var t=arguments[0];this.setComputationPrecision(t.getPrecisionModel()),this._arg=new Array(1).fill(null),this._arg[0]=new fv(0,t)}else if(2===arguments.length){var e=arguments[0],n=arguments[1],r=rh.OGC_SFS_BOUNDARY_RULE;e.getPrecisionModel().compareTo(n.getPrecisionModel())>=0?this.setComputationPrecision(e.getPrecisionModel()):this.setComputationPrecision(n.getPrecisionModel()),this._arg=new Array(2).fill(null),this._arg[0]=new fv(0,e,r),this._arg[1]=new fv(1,n,r)}else if(3===arguments.length){var o=arguments[0],i=arguments[1],a=arguments[2];o.getPrecisionModel().compareTo(i.getPrecisionModel())>=0?this.setComputationPrecision(o.getPrecisionModel()):this.setComputationPrecision(i.getPrecisionModel()),this._arg=new Array(2).fill(null),this._arg[0]=new fv(0,o,a),this._arg[1]=new fv(1,i,a)}};hv.prototype.getArgGeometry=function(t){return this._arg[t].getGeometry()},hv.prototype.setComputationPrecision=function(t){this._resultPrecisionModel=t,this._li.setPrecisionModel(this._resultPrecisionModel)},hv.prototype.interfaces_=function(){return[]},hv.prototype.getClass=function(){return hv};var gv=function(){};gv.prototype.interfaces_=function(){return[]},gv.prototype.getClass=function(){return gv},gv.map=function(){if(arguments[0]instanceof Qf&&_f(arguments[1],gv.MapOp)){for(var t=arguments[0],e=arguments[1],n=new fh,r=0;r<t.getNumGeometries();r++){var o=e.map(t.getGeometryN(r));null!==o&&n.add(o)}return t.getFactory().buildGeometry(n)}if(_f(arguments[0],uh)&&_f(arguments[1],gv.MapOp)){for(var i=arguments[0],a=arguments[1],s=new fh,c=i.iterator();c.hasNext();){var u=c.next(),l=a.map(u);null!==l&&s.add(l)}return s}},gv.MapOp=function(){};var mv=function(t){function e(){var e=arguments[0],n=arguments[1];t.call(this,e,n),this._ptLocator=new gy,this._geomFact=null,this._resultGeom=null,this._graph=null,this._edgeList=new Gm,this._resultPolyList=new fh,this._resultLineList=new fh,this._resultPointList=new fh,this._graph=new Rg(new Rm),this._geomFact=e.getFactory()}return t&&(e.__proto__=t),e.prototype=Object.create(t&&t.prototype),e.prototype.constructor=e,e.prototype.insertUniqueEdge=function(t){var e=this._edgeList.findEqualEdge(t);if(null!==e){var n=e.getLabel(),r=t.getLabel();e.isPointwiseEqual(t)||(r=new Eg(t.getLabel()),r.flip());var o=e.getDepth();o.isNull()&&o.add(n),o.add(r),n.merge(r)}else this._edgeList.add(t)},e.prototype.getGraph=function(){return this._graph},e.prototype.cancelDuplicateResultEdges=function(){for(var t=this._graph.getEdgeEnds().iterator();t.hasNext();){var e=t.next(),n=e.getSym();e.isInResult()&&n.isInResult()&&(e.setInResult(!1),n.setInResult(!1))}},e.prototype.isCoveredByLA=function(t){return!!this.isCovered(t,this._resultLineList)||!!this.isCovered(t,this._resultPolyList)},e.prototype.computeGeometry=function(t,n,r,o){var i=new fh;return i.addAll(t),i.addAll(n),i.addAll(r),i.isEmpty()?e.createEmptyResult(o,this._arg[0].getGeometry(),this._arg[1].getGeometry(),this._geomFact):this._geomFact.buildGeometry(i)},e.prototype.mergeSymLabels=function(){for(var t=this._graph.getNodes().iterator();t.hasNext();){t.next().getEdges().mergeSymLabels()}},e.prototype.isCovered=function(t,e){for(var n=this,r=e.iterator();r.hasNext();){var o=r.next();if(n._ptLocator.locate(t,o)!==yf.EXTERIOR)return!0}return!1},e.prototype.replaceCollapsedEdges=function(){for(var t=new fh,e=this._edgeList.iterator();e.hasNext();){var n=e.next();n.isCollapsed()&&(e.remove(),t.add(n.getCollapsedEdge()))}this._edgeList.addAll(t)},e.prototype.updateNodeLabelling=function(){for(var t=this._graph.getNodes().iterator();t.hasNext();){var e=t.next(),n=e.getEdges().getLabel();e.getLabel().merge(n)}},e.prototype.getResultGeometry=function(t){return this.computeOverlay(t),this._resultGeom},e.prototype.insertUniqueEdges=function(t){for(var e=this,n=t.iterator();n.hasNext();){var r=n.next();e.insertUniqueEdge(r)}},e.prototype.computeOverlay=function(t){this.copyPoints(0),this.copyPoints(1),this._arg[0].computeSelfNodes(this._li,!1),this._arg[1].computeSelfNodes(this._li,!1),this._arg[0].computeEdgeIntersections(this._arg[1],this._li,!0);var e=new fh;this._arg[0].computeSplitEdges(e),this._arg[1].computeSplitEdges(e),this.insertUniqueEdges(e),this.computeLabelsFromDepths(),this.replaceCollapsedEdges(),Ty.checkValid(this._edgeList.getEdges()),this._graph.addEdges(this._edgeList.getEdges()),this.computeLabelling(),this.labelIncompleteNodes(),this.findResultAreaEdges(t),this.cancelDuplicateResultEdges();var n=new Fg(this._geomFact);n.add(this._graph),this._resultPolyList=n.getPolygons();var r=new Fy(this,this._geomFact,this._ptLocator);this._resultLineList=r.build(t);var o=new Gy(this,this._geomFact,this._ptLocator);this._resultPointList=o.build(t),this._resultGeom=this.computeGeometry(this._resultPointList,this._resultLineList,this._resultPolyList,t)},e.prototype.labelIncompleteNode=function(t,e){var n=this._ptLocator.locate(t.getCoordinate(),this._arg[e].getGeometry());t.getLabel().setLocation(e,n)},e.prototype.copyPoints=function(t){for(var e=this,n=this._arg[t].getNodeIterator();n.hasNext();){var r=n.next();e._graph.addNode(r.getCoordinate()).setLabel(t,r.getLabel().getLocation(t))}},e.prototype.findResultAreaEdges=function(t){for(var n=this._graph.getEdgeEnds().iterator();n.hasNext();){var r=n.next(),o=r.getLabel();o.isArea()&&!r.isInteriorAreaEdge()&&e.isResultOfOp(o.getLocation(0,mg.RIGHT),o.getLocation(1,mg.RIGHT),t)&&r.setInResult(!0)}},e.prototype.computeLabelsFromDepths=function(){for(var t=this._edgeList.iterator();t.hasNext();){var e=t.next(),n=e.getLabel(),r=e.getDepth();if(!r.isNull()){r.normalize();for(var o=0;o<2;o++)n.isNull(o)||!n.isArea()||r.isNull(o)||(0===r.getDelta(o)?n.toLine(o):(jf.isTrue(!r.isNull(o,mg.LEFT),"depth of LEFT side has not been initialized"),n.setLocation(o,mg.LEFT,r.getLocation(o,mg.LEFT)),jf.isTrue(!r.isNull(o,mg.RIGHT),"depth of RIGHT side has not been initialized"),n.setLocation(o,mg.RIGHT,r.getLocation(o,mg.RIGHT))))}}},e.prototype.computeLabelling=function(){for(var t=this,e=this._graph.getNodes().iterator();e.hasNext();){e.next().getEdges().computeLabelling(t._arg)}this.mergeSymLabels(),this.updateNodeLabelling()},e.prototype.labelIncompleteNodes=function(){for(var t=this,e=this._graph.getNodes().iterator();e.hasNext();){var n=e.next(),r=n.getLabel();n.isIsolated()&&(r.isNull(0)?t.labelIncompleteNode(n,0):t.labelIncompleteNode(n,1)),n.getEdges().updateLabelling(r)}},e.prototype.isCoveredByA=function(t){return!!this.isCovered(t,this._resultPolyList)},e.prototype.interfaces_=function(){return[]},e.prototype.getClass=function(){return e},e}(hv);mv.overlayOp=function(t,e,n){return new mv(t,e).getResultGeometry(n)},mv.intersection=function(t,e){if(t.isEmpty()||e.isEmpty())return mv.createEmptyResult(mv.INTERSECTION,t,e,t.getFactory());if(t.isGeometryCollection()){var n=e;return Ry.map(t,{interfaces_:function(){return[gv.MapOp]},map:function(t){return t.intersection(n)}})}return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),Hy.overlayOp(t,e,mv.INTERSECTION)},mv.symDifference=function(t,e){if(t.isEmpty()||e.isEmpty()){if(t.isEmpty()&&e.isEmpty())return mv.createEmptyResult(mv.SYMDIFFERENCE,t,e,t.getFactory());if(t.isEmpty())return e.copy();if(e.isEmpty())return t.copy()}return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),Hy.overlayOp(t,e,mv.SYMDIFFERENCE)},mv.resultDimension=function(t,e,n){var r=e.getDimension(),o=n.getDimension(),i=-1;switch(t){case mv.INTERSECTION:i=Math.min(r,o);break;case mv.UNION:i=Math.max(r,o);break;case mv.DIFFERENCE:i=r;break;case mv.SYMDIFFERENCE:i=Math.max(r,o)}return i},mv.createEmptyResult=function(t,e,n,r){var o=null;switch(mv.resultDimension(t,e,n)){case-1:o=r.createGeometryCollection(new Array(0).fill(null));break;case 0:o=r.createPoint();break;case 1:o=r.createLineString();break;case 2:o=r.createPolygon()}return o},mv.difference=function(t,e){return t.isEmpty()?mv.createEmptyResult(mv.DIFFERENCE,t,e,t.getFactory()):e.isEmpty()?t.copy():(t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),Hy.overlayOp(t,e,mv.DIFFERENCE))},mv.isResultOfOp=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=t.getLocation(0),r=t.getLocation(1);return mv.isResultOfOp(n,r,e)}if(3===arguments.length){var o=arguments[0],i=arguments[1],a=arguments[2];switch(o===yf.BOUNDARY&&(o=yf.INTERIOR),i===yf.BOUNDARY&&(i=yf.INTERIOR),a){case mv.INTERSECTION:return o===yf.INTERIOR&&i===yf.INTERIOR;case mv.UNION:return o===yf.INTERIOR||i===yf.INTERIOR;case mv.DIFFERENCE:return o===yf.INTERIOR&&i!==yf.INTERIOR;case mv.SYMDIFFERENCE:return o===yf.INTERIOR&&i!==yf.INTERIOR||o!==yf.INTERIOR&&i===yf.INTERIOR}return!1}},mv.INTERSECTION=1,mv.UNION=2,mv.DIFFERENCE=3,mv.SYMDIFFERENCE=4;var yv=function(){this._g=null,this._boundaryDistanceTolerance=null,this._linework=null,this._ptLocator=new gy,this._seg=new im;var t=arguments[0],e=arguments[1];this._g=t,this._boundaryDistanceTolerance=e,this._linework=this.extractLinework(t)};yv.prototype.isWithinToleranceOfBoundary=function(t){for(var e=this,n=0;n<this._linework.getNumGeometries();n++)for(var r=e._linework.getGeometryN(n),o=r.getCoordinateSequence(),i=0;i<o.size()-1;i++){o.getCoordinate(i,e._seg.p0),o.getCoordinate(i+1,e._seg.p1);var a=e._seg.distance(t);if(a<=e._boundaryDistanceTolerance)return!0}return!1},yv.prototype.getLocation=function(t){return this.isWithinToleranceOfBoundary(t)?yf.BOUNDARY:this._ptLocator.locate(t,this._g)},yv.prototype.extractLinework=function(t){var e=new vv;t.apply(e);var n=e.getLinework(),r=cg.toLineStringArray(n);return t.getFactory().createMultiLineString(r)},yv.prototype.interfaces_=function(){return[]},yv.prototype.getClass=function(){return yv};var vv=function(){this._linework=null,this._linework=new fh};vv.prototype.getLinework=function(){return this._linework},vv.prototype.filter=function(t){var e=this;if(t instanceof jh){var n=t;this._linework.add(n.getExteriorRing());for(var r=0;r<n.getNumInteriorRing();r++)e._linework.add(n.getInteriorRingN(r))}},vv.prototype.interfaces_=function(){return[Mh]},vv.prototype.getClass=function(){return vv};var _v=function(){this._g=null,this._doLeft=!0,this._doRight=!0;var t=arguments[0];this._g=t};_v.prototype.extractPoints=function(t,e,n){for(var r=this,o=t.getCoordinates(),i=0;i<o.length-1;i++)r.computeOffsetPoints(o[i],o[i+1],e,n)},_v.prototype.setSidesToGenerate=function(t,e){this._doLeft=t,this._doRight=e},_v.prototype.getPoints=function(t){for(var e=this,n=new fh,r=hy.getLines(this._g),o=r.iterator();o.hasNext();){var i=o.next();e.extractPoints(i,t,n)}return n},_v.prototype.computeOffsetPoints=function(t,e,n,r){var o=e.x-t.x,i=e.y-t.y,a=Math.sqrt(o*o+i*i),s=n*o/a,c=n*i/a,u=(e.x+t.x)/2,l=(e.y+t.y)/2;if(this._doLeft){var d=new ff(u-c,l+s);r.add(d)}if(this._doRight){var p=new ff(u+c,l-s);r.add(p)}},_v.prototype.interfaces_=function(){return[]},_v.prototype.getClass=function(){return _v};var bv=function t(){this._geom=null,this._locFinder=null,this._location=new Array(3).fill(null),this._invalidLocation=null,this._boundaryDistanceTolerance=t.TOLERANCE,this._testCoords=new fh;var e=arguments[0],n=arguments[1],r=arguments[2];this._boundaryDistanceTolerance=t.computeBoundaryDistanceTolerance(e,n),this._geom=[e,n,r],this._locFinder=[new yv(this._geom[0],this._boundaryDistanceTolerance),new yv(this._geom[1],this._boundaryDistanceTolerance),new yv(this._geom[2],this._boundaryDistanceTolerance)]},wv={TOLERANCE:{configurable:!0}};bv.prototype.reportResult=function(t,e,n){Of.out.println("Overlay result invalid - A:"+yf.toLocationSymbol(e[0])+" B:"+yf.toLocationSymbol(e[1])+" expected:"+(n?"i":"e")+" actual:"+yf.toLocationSymbol(e[2]))},bv.prototype.isValid=function(t){return this.addTestPts(this._geom[0]),this.addTestPts(this._geom[1]),this.checkValid(t)},bv.prototype.checkValid=function(){var t=this;if(1===arguments.length){for(var e=arguments[0],n=0;n<this._testCoords.size();n++){var r=t._testCoords.get(n);if(!t.checkValid(e,r))return t._invalidLocation=r,!1}return!0}if(2===arguments.length){var o=arguments[0],i=arguments[1];return this._location[0]=this._locFinder[0].getLocation(i),this._location[1]=this._locFinder[1].getLocation(i),this._location[2]=this._locFinder[2].getLocation(i),!!bv.hasLocation(this._location,yf.BOUNDARY)||this.isValidResult(o,this._location)}},bv.prototype.addTestPts=function(t){var e=new _v(t);this._testCoords.addAll(e.getPoints(5*this._boundaryDistanceTolerance))},bv.prototype.isValidResult=function(t,e){var n=mv.isResultOfOp(e[0],e[1],t),r=e[2]===yf.INTERIOR,o=!(n^r);return o||this.reportResult(t,e,n),o},bv.prototype.getInvalidLocation=function(){return this._invalidLocation},bv.prototype.interfaces_=function(){return[]},bv.prototype.getClass=function(){return bv},bv.hasLocation=function(t,e){for(var n=0;n<3;n++)if(t[n]===e)return!0;return!1},bv.computeBoundaryDistanceTolerance=function(t,e){return Math.min(Uy.computeSizeBasedSnapTolerance(t),Uy.computeSizeBasedSnapTolerance(e))},bv.isValid=function(t,e,n,r){return new bv(t,e,r).isValid(n)},wv.TOLERANCE.get=function(){return 1e-6},Object.defineProperties(bv,wv);var xv=function t(e){this._geomFactory=null,this._skipEmpty=!1,this._inputGeoms=null,this._geomFactory=t.extractFactory(e),this._inputGeoms=e};xv.prototype.extractElements=function(t,e){var n=this;if(null===t)return null;for(var r=0;r<t.getNumGeometries();r++){var o=t.getGeometryN(r);n._skipEmpty&&o.isEmpty()||e.add(o)}},xv.prototype.combine=function(){for(var t=this,e=new fh,n=this._inputGeoms.iterator();n.hasNext();){var r=n.next();t.extractElements(r,e)}return 0===e.size()?null!==this._geomFactory?this._geomFactory.createGeometryCollection(null):null:this._geomFactory.buildGeometry(e)},xv.prototype.interfaces_=function(){return[]},xv.prototype.getClass=function(){return xv},xv.combine=function(){if(1===arguments.length){var t=arguments[0];return new xv(t).combine()}if(2===arguments.length){var e=arguments[0],n=arguments[1];return new xv(xv.createList(e,n)).combine()}if(3===arguments.length){var r=arguments[0],o=arguments[1],i=arguments[2];return new xv(xv.createList(r,o,i)).combine()}},xv.extractFactory=function(t){return t.isEmpty()?null:t.iterator().next().getFactory()},xv.createList=function(){if(2===arguments.length){var t=arguments[0],e=arguments[1],n=new fh;return n.add(t),n.add(e),n}if(3===arguments.length){var r=arguments[0],o=arguments[1],i=arguments[2],a=new fh;return a.add(r),a.add(o),a.add(i),a}};var Ev=function(){this._inputPolys=null,this._geomFactory=null;var t=arguments[0];this._inputPolys=t,null===this._inputPolys&&(this._inputPolys=new fh)},Cv={STRTREE_NODE_CAPACITY:{configurable:!0}};Ev.prototype.reduceToGeometries=function(t){for(var e=this,n=new fh,r=t.iterator();r.hasNext();){var o=r.next(),i=null;_f(o,ph)?i=e.unionTree(o):o instanceof Qf&&(i=o),n.add(i)}return n},Ev.prototype.extractByEnvelope=function(t,e,n){for(var r=new fh,o=0;o<e.getNumGeometries();o++){var i=e.getGeometryN(o);i.getEnvelopeInternal().intersects(t)?r.add(i):n.add(i)}return this._geomFactory.buildGeometry(r)},Ev.prototype.unionOptimized=function(t,e){var n=t.getEnvelopeInternal(),r=e.getEnvelopeInternal();if(!n.intersects(r)){return xv.combine(t,e)}if(t.getNumGeometries()<=1&&e.getNumGeometries()<=1)return this.unionActual(t,e);var o=n.intersection(r);return this.unionUsingEnvelopeIntersection(t,e,o)},Ev.prototype.union=function(){if(null===this._inputPolys)throw new Error("union() method cannot be called twice");if(this._inputPolys.isEmpty())return null;this._geomFactory=this._inputPolys.iterator().next().getFactory();for(var t=new Kg(Ev.STRTREE_NODE_CAPACITY),e=this._inputPolys.iterator();e.hasNext();){var n=e.next();t.insert(n.getEnvelopeInternal(),n)}this._inputPolys=null;var r=t.itemsTree();return this.unionTree(r)},Ev.prototype.binaryUnion=function(){if(1===arguments.length){var t=arguments[0];return this.binaryUnion(t,0,t.size())}if(3===arguments.length){var e=arguments[0],n=arguments[1],r=arguments[2];if(r-n<=1){var o=Ev.getGeometry(e,n);return this.unionSafe(o,null)}if(r-n==2)return this.unionSafe(Ev.getGeometry(e,n),Ev.getGeometry(e,n+1));var i=Math.trunc((r+n)/2),a=this.binaryUnion(e,n,i),s=this.binaryUnion(e,i,r);return this.unionSafe(a,s)}},Ev.prototype.repeatedUnion=function(t){for(var e=null,n=t.iterator();n.hasNext();){var r=n.next();e=null===e?r.copy():e.union(r)}return e},Ev.prototype.unionSafe=function(t,e){return null===t&&null===e?null:null===t?e.copy():null===e?t.copy():this.unionOptimized(t,e)},Ev.prototype.unionActual=function(t,e){return Ev.restrictToPolygons(t.union(e))},Ev.prototype.unionTree=function(t){var e=this.reduceToGeometries(t);return this.binaryUnion(e)},Ev.prototype.unionUsingEnvelopeIntersection=function(t,e,n){var r=new fh,o=this.extractByEnvelope(n,t,r),i=this.extractByEnvelope(n,e,r),a=this.unionActual(o,i);return r.add(a),xv.combine(r)},Ev.prototype.bufferUnion=function(){if(1===arguments.length){var t=arguments[0];return t.get(0).getFactory().buildGeometry(t).buffer(0)}if(2===arguments.length){var e=arguments[0],n=arguments[1];return e.getFactory().createGeometryCollection([e,n]).buffer(0)}},Ev.prototype.interfaces_=function(){return[]},Ev.prototype.getClass=function(){return Ev},Ev.restrictToPolygons=function(t){if(_f(t,zh))return t;var e=fy.getPolygons(t);return 1===e.size()?e.get(0):t.getFactory().createMultiPolygon(cg.toPolygonArray(e))},Ev.getGeometry=function(t,e){return e>=t.size()?null:t.get(e)},Ev.union=function(t){return new Ev(t).union()},Cv.STRTREE_NODE_CAPACITY.get=function(){return 4},Object.defineProperties(Ev,Cv);var Sv=function(){};Sv.prototype.interfaces_=function(){return[]},Sv.prototype.getClass=function(){return Sv},Sv.union=function(t,e){if(t.isEmpty()||e.isEmpty()){if(t.isEmpty()&&e.isEmpty())return mv.createEmptyResult(mv.UNION,t,e,t.getFactory());if(t.isEmpty())return e.copy();if(e.isEmpty())return t.copy()}return t.checkNotGeometryCollection(t),t.checkNotGeometryCollection(e),Hy.overlayOp(t,e,mv.UNION)};var Iv=function(){return new Ic};Ic.prototype={constructor:Ic,reset:function(){this.s=this.t=0},add:function(t){Pc(Pv,t,this.t),Pc(this,Pv.s,this.s),this.s?this.t+=Pv.t:this.s=Pv.t},valueOf:function(){return this.s}};var Pv=new Ic,Av=1e-6,Nv=Math.PI,Mv=Nv/2,Lv=Nv/4,Dv=2*Nv,Ov=180/Nv,Tv=Nv/180,Rv=Math.abs,Fv=Math.atan,Gv=Math.atan2,qv=Math.cos,kv=Math.exp,Uv=Math.log,Bv=Math.sin,Vv=Math.sqrt,zv=Math.tan,jv={Feature:function(t,e){Lc(t.geometry,e)},FeatureCollection:function(t,e){for(var n=t.features,r=-1,o=n.length;++r<o;)Lc(n[r].geometry,e)}},Yv={Sphere:function(t,e){e.sphere()},Point:function(t,e){t=t.coordinates,e.point(t[0],t[1],t[2])},MultiPoint:function(t,e){for(var n=t.coordinates,r=-1,o=n.length;++r<o;)t=n[r],e.point(t[0],t[1],t[2])},LineString:function(t,e){Dc(t.coordinates,e,0)},MultiLineString:function(t,e){for(var n=t.coordinates,r=-1,o=n.length;++r<o;)Dc(n[r],e,0)},Polygon:function(t,e){Oc(t.coordinates,e)},MultiPolygon:function(t,e){for(var n=t.coordinates,r=-1,o=n.length;++r<o;)Oc(n[r],e)},GeometryCollection:function(t,e){for(var n=t.geometries,r=-1,o=n.length;++r<o;)Lc(n[r],e)}},Xv=function(t,e){t&&jv.hasOwnProperty(t.type)?jv[t.type](t,e):Lc(t,e)},Wv=(Iv(),Iv(),Iv(),function(t,e){function n(n,r){return n=t(n,r),e(n[0],n[1])}return t.invert&&e.invert&&(n.invert=function(n,r){return(n=e.invert(n,r))&&t.invert(n[0],n[1])}),n});Bc.invert=Bc;var $v=function(t){function e(e){return e=t(e[0]*Tv,e[1]*Tv),e[0]*=Ov,e[1]*=Ov,e}return t=Vc(t[0]*Tv,t[1]*Tv,t.length>2?t[2]*Tv:0),e.invert=function(e){return e=t.invert(e[0]*Tv,e[1]*Tv),e[0]*=Ov,e[1]*=Ov,e},e},Hv=function(){var t,e=[];return{point:function(e,n){t.push([e,n])},lineStart:function(){e.push(t=[])},lineEnd:Mc,rejoin:function(){e.length>1&&e.push(e.pop().concat(e.shift()))},result:function(){var n=e;return e=[],t=null,n}}},Kv=function(t,e,n,r,o,i){var a,s=t[0],c=t[1],u=e[0],l=e[1],d=0,p=1,f=u-s,h=l-c;if(a=n-s,f||!(a>0)){if(a/=f,f<0){if(a<d)return;a<p&&(p=a)}else if(f>0){if(a>p)return;a>d&&(d=a)}if(a=o-s,f||!(a<0)){if(a/=f,f<0){if(a>p)return;a>d&&(d=a)}else if(f>0){if(a<d)return;a<p&&(p=a)}if(a=r-c,h||!(a>0)){if(a/=h,h<0){if(a<d)return;a<p&&(p=a)}else if(h>0){if(a>p)return;a>d&&(d=a)}if(a=i-c,h||!(a<0)){if(a/=h,h<0){if(a>p)return;a>d&&(d=a)}else if(h>0){if(a<d)return;a<p&&(p=a)}return d>0&&(t[0]=s+d*f,t[1]=c+d*h),p<1&&(e[0]=s+p*f,e[1]=c+p*h),!0}}}}},Jv=function(t,e){return Rv(t[0]-e[0])<Av&&Rv(t[1]-e[1])<Av},Zv=function(t,e,n,r,o){var i,a,s=[],c=[];if(t.forEach(function(t){if(!((e=t.length-1)<=0)){var e,n,r=t[0],a=t[e];if(Jv(r,a)){for(o.lineStart(),i=0;i<e;++i)o.point((r=t[i])[0],r[1]);return void o.lineEnd()}s.push(n=new $c(r,t,null,!0)),c.push(n.o=new $c(r,null,n,!1)),s.push(n=new $c(a,t,null,!1)),c.push(n.o=new $c(a,null,n,!0))}}),s.length){for(c.sort(e),Hc(s),Hc(c),i=0,a=c.length;i<a;++i)c[i].e=n=!n;for(var u,l,d=s[0];;){for(var p=d,f=!0;p.v;)if((p=p.n)===d)return;u=p.z,o.lineStart();do{if(p.v=p.o.v=!0,p.e){if(f)for(i=0,a=u.length;i<a;++i)o.point((l=u[i])[0],l[1]);else r(p.x,p.n.x,1,o);p=p.n}else{if(f)for(u=p.p.z,i=u.length-1;i>=0;--i)o.point((l=u[i])[0],l[1]);else r(p.x,p.p.x,-1,o);p=p.p}p=p.o,u=p.z,f=!f}while(!p.v);o.lineEnd()}}},Qv=function(t,e){return t<e?-1:t>e?1:t>=e?0:NaN},t_=(function(t){1===t.length&&(t=Kc(t))}(Qv),function(t){for(var e,n,r,o=t.length,i=-1,a=0;++i<o;)a+=t[i].length;for(n=new Array(a);--o>=0;)for(r=t[o],e=r.length;--e>=0;)n[--a]=r[e];return n}),e_=1e9,n_=-e_,r_=Iv(),o_=function(t,e){var n=e[0],r=e[1],o=[Bv(n),-qv(n),0],i=0,a=0;r_.reset();for(var s=0,c=t.length;s<c;++s)if(l=(u=t[s]).length)for(var u,l,d=u[l-1],p=d[0],f=d[1]/2+Lv,h=Bv(f),g=qv(f),m=0;m<l;++m,p=v,h=b,g=w,d=y){var y=u[m],v=y[0],_=y[1]/2+Lv,b=Bv(_),w=qv(_),x=v-p,E=x>=0?1:-1,C=E*x,S=C>Nv,I=h*b;if(r_.add(Gv(I*E*Bv(C),g*w+I*qv(C))),i+=S?x+E*Dv:x,S^p>=n^v>=n){var P=Gc(Rc(d),Rc(y));Uc(P);var A=Gc(o,P);Uc(A);var N=(S^x>=0?-1:1)*Nc(A[2]);(r>N||r===N&&(P[0]||P[1]))&&(a+=S^x>=0?1:-1)}}return(i<-Av||i<Av&&r_<-Av)^1&a},i_=(Iv(),function(t){return t}),a_=(Iv(),Iv(),1/0),s_=a_,c_=-a_,u_=c_,l_={point:Zc,lineStart:Mc,lineEnd:Mc,polygonStart:Mc,polygonEnd:Mc,result:function(){var t=[[a_,s_],[c_,u_]];return c_=u_=-(s_=a_=1/0),t}},d_=(Iv(),function(t,e,n,r){return function(o,i){function a(e,n){var r=o(e,n);t(e=r[0],n=r[1])&&i.point(e,n)}function s(t,e){var n=o(t,e);m.point(n[0],n[1])}function c(){w.point=s,m.lineStart()}function u(){w.point=a,m.lineEnd()}function l(t,e){g.push([t,e]);var n=o(t,e);_.point(n[0],n[1])}function d(){_.lineStart(),g=[]}function p(){l(g[0][0],g[0][1]),_.lineEnd();var t,e,n,r,o=_.clean(),a=v.result(),s=a.length;if(g.pop(),f.push(g),g=null,s)if(1&o){if(n=a[0],(e=n.length-1)>0){for(b||(i.polygonStart(),b=!0),i.lineStart(),t=0;t<e;++t)i.point((r=n[t])[0],r[1]);i.lineEnd()}}else s>1&&2&o&&a.push(a.pop().concat(a.shift())),h.push(a.filter(Qc))}var f,h,g,m=e(i),y=o.invert(r[0],r[1]),v=Hv(),_=e(v),b=!1,w={point:a,lineStart:c,lineEnd:u,polygonStart:function(){w.point=l,w.lineStart=d,w.lineEnd=p,h=[],f=[]},polygonEnd:function(){w.point=a,w.lineStart=c,w.lineEnd=u,h=t_(h);var t=o_(f,y);h.length?(b||(i.polygonStart(),b=!0),Zv(h,tu,t,n,i)):t&&(b||(i.polygonStart(),b=!0),i.lineStart(),n(null,null,1,i),i.lineEnd()),b&&(i.polygonEnd(),b=!1),h=f=null},sphere:function(){i.polygonStart(),i.lineStart(),n(null,null,1,i),i.lineEnd(),i.polygonEnd()}};return w}}),p_=d_(function(){return!0},eu,ru,[-Nv,-Mv]),f_=function(t,e){function n(n,r,o,i){Xc(i,t,e,o,n,r)}function r(t,e){return qv(t)*qv(e)>s}function o(t){var e,n,o,s,l;return{lineStart:function(){s=o=!1,l=1},point:function(d,p){var f,h=[d,p],g=r(d,p),m=c?g?0:a(d,p):g?a(d+(d<0?Nv:-Nv),p):0;if(!e&&(s=o=g)&&t.lineStart(),g!==o&&(!(f=i(e,h))||Jv(e,f)||Jv(h,f))&&(h[0]+=Av,h[1]+=Av,g=r(h[0],h[1])),g!==o)l=0,g?(t.lineStart(),f=i(h,e),t.point(f[0],f[1])):(f=i(e,h),t.point(f[0],f[1]),t.lineEnd()),e=f;else if(u&&e&&c^g){var y;m&n||!(y=i(h,e,!0))||(l=0,c?(t.lineStart(),t.point(y[0][0],y[0][1]),t.point(y[1][0],y[1][1]),t.lineEnd()):(t.point(y[1][0],y[1][1]),t.lineEnd(),t.lineStart(),t.point(y[0][0],y[0][1])))}!g||e&&Jv(e,h)||t.point(h[0],h[1]),e=h,o=g,n=m},lineEnd:function(){o&&t.lineEnd(),e=null},clean:function(){return l|(s&&o)<<1}}}function i(t,e,n){var r=Rc(t),o=Rc(e),i=[1,0,0],a=Gc(r,o),c=Fc(a,a),u=a[0],l=c-u*u;if(!l)return!n&&t;var d=s*c/l,p=-s*u/l,f=Gc(i,a),h=kc(i,d);qc(h,kc(a,p));var g=f,m=Fc(h,g),y=Fc(g,g),v=m*m-y*(Fc(h,h)-1);if(!(v<0)){var _=Vv(v),b=kc(g,(-m-_)/y);if(qc(b,h),b=Tc(b),!n)return b;var w,x=t[0],E=e[0],C=t[1],S=e[1];E<x&&(w=x,x=E,E=w);var I=E-x,P=Rv(I-Nv)<Av,A=P||I<Av;if(!P&&S<C&&(w=C,C=S,S=w),A?P?C+S>0^b[1]<(Rv(b[0]-x)<Av?C:S):C<=b[1]&&b[1]<=S:I>Nv^(x<=b[0]&&b[0]<=E)){var N=kc(g,(-m+_)/y);return qc(N,h),[b,Tc(N)]}}}function a(e,n){var r=c?t:Nv-t,o=0;return e<-r?o|=1:e>r&&(o|=2),n<-r?o|=4:n>r&&(o|=8),o}var s=qv(t),c=s>0,u=Rv(s)>Av;return d_(r,o,n,c?[0,-t]:[-Nv,t-Nv])};iu.prototype={constructor:iu,point:function(t,e){this.stream.point(t,e)},sphere:function(){this.stream.sphere()},lineStart:function(){this.stream.lineStart()},lineEnd:function(){this.stream.lineEnd()},polygonStart:function(){this.stream.polygonStart()},polygonEnd:function(){this.stream.polygonEnd()}};var h_=16,g_=qv(30*Tv),m_=function(t,e){return+e?uu(t,e):cu(t)},y_=ou({point:function(t,e){this.stream.point(t*Tv,e*Tv)}});pu.invert=function(t,e){return[t,2*Fv(kv(e))-Mv]},hu.invert=function(t,e){return[-e,2*Fv(kv(t))-Mv]};var v_=function(){var t=fu(hu),e=t.center,n=t.rotate;return t.center=function(t){return arguments.length?e([-t[1],t[0]]):(t=e(),[t[1],-t[0]])},t.rotate=function(t){return arguments.length?n([t[0],t[1],t.length>2?t[2]+90:90]):(t=n(),[t[0],t[1],t[2]-90])},n([0,0,90]).scale(159.155)};t.projection=Fl,t.random=rf,t.clusters=of,t.helpers=ju,t.invariant=Xu,t.meta=Yu,t.isolines=mt,t.convex=se,t.pointsWithinPolygon=de,t.concave=Be,t.collect=ze,t.flip=je,t.simplify=en,t.bezierSpline=sn,t.tag=cn,t.sample=un,t.envelope=pn,t.square=fn,t.circle=gn,t.midpoint=vn,t.center=_n,t.centerOfMass=wn,t.centroid=bn,t.combine=xn,t.distance=ye,t.explode=En,t.bbox=H,t.tesselate=Qn,t.bboxPolygon=dn,t.booleanPointInPolygon=ce,t.nearestPoint=nr,t.nearestPointOnLine=Lr,t.nearestPointToLine=Wr,t.planepoint=Hr,t.tin=pe,t.bearing=mn,t.destination=hn,t.kinks=Kr,t.pointOnFeature=Zr,t.area=eo,t.along=ao,t.length=so,t.lineSlice=co,t.lineSliceAlong=uo,t.pointGrid=Co,t.truncate=So,t.flatten=Po,t.lineIntersect=Nr,t.lineChunk=Ao,t.unkinkPolygon=Fo,t.greatCircle=Go,t.lineSegment=Sr,t.lineSplit=qo,t.lineArc=zo,t.polygonToLine=Yo,t.lineToPolygon=Wo,t.bboxClip=ei,t.lineOverlap=ui,t.sector=di,t.rhumbBearing=Dr,t.rhumbDistance=Tr,t.rhumbDestination=fi,t.polygonTangents=gi,t.rewind=wi,t.isobands=Gi,t.transformRotate=ji,t.transformScale=Yi,t.transformTranslate=$i,t.lineOffset=ra,t.polygonize=da,t.booleanDisjoint=pa,t.booleanContains=ba,t.booleanCrosses=La,t.booleanClockwise=bi,t.booleanOverlap=Ua,t.booleanPointOnLine=lo,t.booleanEqual=Ba,t.booleanWithin=fo,t.clone=ve,t.cleanCoords=Ye,t.clustersDbscan=Va,t.clustersKmeans=Ya,t.pointToLineDistance=Vr,t.booleanParallel=Xa,t.shortestPath=ts,t.voronoi=Rs,t.ellipse=Fs,t.centerMean=qs,t.centerMedian=ks,t.standardDeviationalEllipse=Bs,t.difference=Cc,t.buffer=gu,t.union=wu,t.intersect=xu,t.dissolve=Su,t.hexGrid=Pu,t.mask=Mu,t.squareGrid=Gu,t.triangleGrid=qu,t.interpolate=ku,t.pointOnSurface=Zr,t.polygonToLineString=Yo,t.lineStringToPolygon=Wo,t.inside=ce,t.within=de,t.bezier=sn,t.nearest=nr,t.pointOnLine=Lr,t.lineDistance=so,t.radians2degrees=_,t.degrees2radians=b,t.distanceToDegrees=y,t.distanceToRadians=m,t.radiansToDistance=g,t.bearingToAngle=v,t.convertDistance=w,t.toMercator=Fr,t.toWgs84=Gr,t.randomPosition=zs,t.randomPoint=js,t.randomPolygon=Ys,t.randomLineString=Xs,t.getCluster=Zs,t.clusterEach=Qs,t.clusterReduce=tc,t.createBins=ec,t.applyFilter=nc,t.propertiesContainsFilter=rc,t.filterProperties=oc,t.earthRadius=Uu,t.factors=Bu,t.unitsFactors=Vu,t.areaFactors=zu,t.feature=e,t.geometry=n,t.point=r,t.points=o,t.polygon=i,t.polygons=a,t.lineString=s,t.lineStrings=c,t.featureCollection=u,t.multiLineString=l,t.multiPoint=d,t.multiPolygon=p,t.geometryCollection=f,t.round=h,t.radiansToLength=g,t.lengthToRadians=m,t.lengthToDegrees=y,t.bearingToAzimuth=v,t.radiansToDegrees=_,t.degreesToRadians=b,t.convertLength=w,t.convertArea=x,t.isNumber=E,t.isObject=C,t.validateBBox=S,t.validateId=I,t.getCoord=K,t.getCoords=J,t.containsNumber=Z,t.geojsonType=Q,t.featureOf=tt,t.collectionOf=et,t.getGeom=nt,t.getGeomType=rt,t.getType=ot,t.coordEach=T,t.coordReduce=R,t.propEach=F,t.propReduce=G,t.featureEach=q,t.featureReduce=k,t.coordAll=U,t.geomEach=B,t.geomReduce=V,t.flattenEach=z,t.flattenReduce=j,t.segmentEach=Y,t.segmentReduce=X,t.lineEach=W,t.lineReduce=$,Object.defineProperty(t,"__esModule",{value:!0})}),function(){"use strict";angular.module("suApp",[]).constant("config",{apiUrl:"/api"}),angular.module("su-geoidentificacao",["su-componentes","ui.router","ngResource","ngMaterial","ngMdIcons","ngMessages","md.data.table","ui.utils.masks","ui.mask","vAccordion","suApp"]).constant("giConfig",{apiUrl:"/geoidentificacao/api/",geometadadosUrl:"/geometadados/api/",geoservicosUrl:"/geoservicos/api/",geoserverUrl:"/geoserver/"}).config(["$mdDateLocaleProvider","$suMapProvider","giConfig",function(t,e,n){e.addServer("vetor",n.geoserverUrl)}]).value("urlPrefixo","/geoidentificacao")}(),function(){"use strict";angular.module("su-geoidentificacao").factory("giLoading",["$mdDialog",function(t){function e(){this.isLoading=!0,t.show({template:'<md-dialog style="background-color:transparent;box-shadow:none;overflow: hidden !important;"><div layout="row" layout-sm="column" layout-align="center center" aria-label="wait"><md-progress-circular class="md-hue-2" md-diameter="50px"></md-progress-circular></div></md-dialog>',parent:angular.element(document.body),clickOutsideToClose:!1,fullscreen:!1})}function n(){t.cancel(),this.isLoading=!1}function r(t){t.onkeyup=function(){t.maxLength=10;var e=new RegExp("/o","g"),n=t.value.replace(e,"");isNaN(n)?t.value=o(t.value.replace(/\D/g,"")):t.value=o(t.value)}}function o(t){var e=t;return t.length>2&&(e=[t.slice(0,2),"/",t.slice(2)].join("")),t.length>4&&(e=[e.slice(0,5),"/",e.slice(5)].join("")),e}return{showLoading:e,isLoading:!1,hideLoading:n,formataDataRegra:r}}])}(),function(){"use strict";var t=angular.module("su-geoidentificacao").component("upload",{templateUrl:"scripts-geoidentificacao/upload/upload.html"});t.directive("fileModel",["$parse",function(t){return{restrict:"A",link:function(e,n,r){var o=t(r.fileModel),i=o.assign;n.bind("change",function(){e.$apply(function(){i(e,n[0].files[0])})})}}}]),t.service("fileUpload",["$http","giOlFunctions","$rootScope",function(t,e,n){this.uploadFileToUrl=function(r,o){var i=n,a=new FormData;a.append("file",r),t.post(o,a,{transformRequest:angular.identity,headers:{"Content-Type":void 0}}).then(function(t){i.geometria=t.data.data.data.toString(),
viewer.getLayer("vectorCertos")&&viewer.getLayer("vectorCertos").getSource().clear(),viewer.getLayer("vectorIntercessoes")&&viewer.getLayer("vectorIntercessoes").getSource().clear(),viewer.getLayer("vectorErrados")&&viewer.getLayer("vectorErrados").getSource().clear(),viewer.getLayer("vectorForaPais")&&viewer.getLayer("vectorForaPais").getSource().clear(),viewer.getLayer("vectorGeomCadastrados")&&viewer.getLayer("vectorGeomCadastrados").getSource().clear(),e.desenhaArquivo(t.data.data.data.toString())})}}]),t.controller("myCtrl",["$scope","fileUpload","giConfig",function(t,e,n){t.uploadFile=function(){var r=t.myFile,o=n.geoservicosUrl+"upload/uploadArquivosProcessoDemarcatorio";e.uploadFileToUrl(r,o)}}])}(),function(){"use strict";angular.module("su-geoidentificacao").provider("$suMap",function(){function t(t,e){n.servers[t]=e}function e(t){return n.servers[t]}var n=this;return this.servers={},{$get:function(){return{servers:n.servers,getServer:e,addServer:t}},addServer:t}})}(),function(){"use strict";angular.module("su-geoidentificacao").component("suMap",{templateUrl:"scripts-geoidentificacao/su_map/su_map.html",transclude:!0,bindings:{mapId:"@",mviewer:"=",mostrarQuery:"<"},controller:["$scope","$element","$rootScope","giSpuviewer","giService","$mdDialog","giConfig","mapService",function(t,e,n,r,o,i,a,s){function c(t,e){t.getViewport().oncontextmenu=null}var u=t,l=this;u.searchQuery="",u.popup={},this.$onInit=function(){u.mapid=this.mapId||"map",e.find("div")[0].id=u.mapid,!1===l.mostrarQuery?u.mostrarQuery=!1:u.mostrarQuery=!0,u.mviewer=this.mviewer||{},o.appParametros.get().$promise.then(function(e){URL=e.URL_GEOSERVER_VETOR;var n=new ol.layer.Tile({source:new ol.source.OSM});l.viewer=new r.sv.SPUViewer({servers:{vetor:{url:URL,workspace:"ide"}},target:u.mapid}),l.mviewer.viewer=l.viewer,setTimeout(function(){l.viewer.init(),l.viewer.addLayer("camadaBase",n),l.viewer.addLayerWMS("vetor","uf",!0),l.viewer.addLayerWMS("vetor","municipio",!0,null,.01),l.viewer.addLayerWMS("vetor","tb_areas_uniao",!0),overlayPopUPIntersecoesGeo=new ol.Overlay({element:d,autoPan:!0,autoPanAnimation:{duration:250}}),l.viewer.getMap().addOverlay(overlayPopUPIntersecoesGeo),l.viewer.getMap().updateSize(),c(l.viewer.getMap(),l.viewer.getView()),t.$emit("mapaIniciado")},3e3)})};var d=document.getElementById("popupIntersecoes");document.getElementById("popup-contentIntersecoes"),document.getElementById("popup-closerIntersecoes");u.close=function(){return overlayPopUPIntersecoesGeo.setPosition(void 0),closer.blur(),!1},t.$on("popupConteudoIntersecoesGeo",function(t,e){u.popup=e.responsePopup,u.showPopupIntersecoesGeo=!0}),u.getListaUfsFormatados=function(t){if(!t)return"";for(var e=[],n=0;n<t.length;n++){var r=t[n];r&&r.uf&&e.push(r.uf.sgUf)}return e.length>0?e.join(", "):""},u.getListaMunicipiosFormatados=function(t){if(!t)return"";for(var e=[],n=0;n<t.length;n++){var r=t[n];r&&r.noAreaPoliticoAdministrativa&&e.push(r.noAreaPoliticoAdministrativa)}return e.length>0?e.join(", "):""},t.$on("popupConteudo",function(e,n){var r=l.viewer.getMap(),i=n.responsePopup;u.popup.ufs=[],u.popup.municipios=[],o.buscarMunicipiosPorGeometria(i.geomWkt).then(function(t){var e=new Set,n=[];if(200===t.status){for(var r=t.data,o=0;o<r.length;o++){var i=r[o];n.push(i.nome),e.add(i.uf)}var a=[];angular.forEach(Array.from(e.values()),function(t){a.push(t)}),u.popup.municipios=n.join(", "),u.popup.ufs=a.join(", ")}else console.debug("Erro ao buscar municipios: ",t.data)}),u.popup.tipo=i.tipoDemarcacao.noTipoDemarcacao,u.popup.trecho=i.dsTrechoInicio&&i.dsTrechoFim?"inicial: "+i.dsTrechoInicio+"final: "+i.dsTrechoFim:i.dsTrecho,u.popup.portariaComissaoDemarcacao=i.portariaComissaoDemarcacao,u.popup.etapa=i.etapaDemarcacao.noEtapaDemarcacao,u.popup.situacao="true"===i.icSituacaoDemarcacao?"Demarcado":"Não demarcado",u.popup.demandaGeradora=i.finalidadeDemarcacao.noFinalidadeDemarcacao,u.popup.areaAproximada=i.areaAproximada,u.popup.nivelPrecisaoCartografia=i.niveisPrecisaoCartografica.dsNiveisPrecisaoCartografica,u.popup.nuProcessoDemarcatorio=i.nuProcessoDemarcatorio,u.popup.noCadastrador=i.noCadastrador,u.popup.dtCadastro=i.dtCadastro,u.popup.showPopup=!0;var a=document.getElementById("popup"),s=document.getElementById("popup-closer");s.onclick=function(){return c.setPosition(void 0),s.blur(),!1};var c=new ol.Overlay({element:a,autoPan:!0,autoPanAnimation:{duration:250}});t.$emit("overlayCriado",{overlayPopUP:c}),r.addOverlay(c)});t.alterarVisibilidadeSateliteLayer=function(){t.sateliteLayer.setVisible(!t.sateliteLayer.getVisible())};var p=new ol.source.TileWMS({url:"localhost:8686",params:{LAYERS:"ide:tb_areas_uniao",TILED:!0},serverType:"geoserver",crossOrigin:"anonymous"});new ol.layer.Tile({source:p});u.executeQuery=function(){""!==u.searchQuery&&s.buscarLocalidadesPorCoordenadas(u.searchQuery).then(function(t){var e,r=t.data;if(200===t.status&&t.data.length>0){for(var o=0;o<r.length-1;o++)if("Polygon"==r[o].geojson.type){e=r[o];break}var i=e.geojson.coordinates,a=new ol.geom.Polygon(i);a.getProperties();l.viewer.setZoom(a.getExtent());var s=a.getCoordinates()[0].join("/").replaceAll(","," ").replaceAll("/",", ");n.$broadcast("buscarLocalidade",{geometria:"POLYGON (("+s+"))"})}}).finally(function(){u.searchQuery=""})};var f=angular.element(document.body),h=new ol.interaction.MouseWheelZoom;f.bind("click",function(t){var e=l.viewer.getMap().getInteractions();"CANVAS"==t.target.nodeName?-1==e.getArray().indexOf(h)&&l.viewer.getMap().addInteraction(h):l.viewer.getMap().removeInteraction(h)})}]})}(),function(){"use strict";angular.module("su-geoidentificacao").factory("olUtils",function(){function t(t,e){if(e=n(e)){o(t,r(e));a(t,i(e,t.getSize()))}}function e(t,e){if(e=n(e)){var o=r(e);t.getView().setCenter(o);var a=i(e,t.getSize());t.getView().setResolution(a)}}function n(t){if("string"==typeof t&&("BOX"===t.substring(0,3)?t=t.replace("BOX(","").replace(")","").replace(","," ").split(" "):"Env"===t.substring(0,3)&&(t=t.replace("Env[","").replace("]","").replace(/ /g,"").replace(/:/g," ").replace(/,/," ").split(" "),t=[t[0],t[2],t[1],t[3]])),t instanceof Array)for(var e=0;e<t.length;e++)t[e]=parseFloat(t[e]);return t}function r(t){return ol.extent.getCenter(t)}function o(t,e){var n=Number(new Date),r=ol.animation.pan({duration:l,source:t.getView().getCenter(),start:n});t.beforeRender(r),t.getView().setCenter(e)}function i(t,e){var n=ol.extent.getWidth(t)/e[0],r=ol.extent.getHeight(t)/e[1];return Math.max(n,r)}function a(t,e){var n=Number(new Date),r=ol.animation.zoom({duration:l,resolution:t.getView().getResolution(),start:n});t.beforeRender(r),t.getView().setResolution(e)}function s(t){var e=[];return t.forEach(function(t){t=n(t);var r=t,o=[r[0],r[1]],i=[r[2],r[3]];e.push(o),e.push(i)}),ol.extent.boundingExtent(e)}function c(t){return(new ol.format.WKT).readFeature(t)}function u(t,e){var n=new ol.format.WKT,r=n.readFeature(e),o=new ol.layer.Vector({source:new ol.source.Vector({features:[r]})});t.addLayer("draw",o),this.zoomByExtent(r.getGeometry().getExtent())}var l=2e3;return{zoomByExtent:t,zoomByExtentNoAnimation:e,extent2array:n,panAnimation:o,getCenterByExtent:r,getResolutionForExtent:i,zoomAnimation:a,extentFeaturesExtent:s,wkt2feature:c,desenhaArquivo:u}})}();var dess,lol;!function(){"use strict";angular.module("su-geoidentificacao").factory("giSpuviewer",["olUtils",function(t){var e={},n=new ol.View({projection:"EPSG:4326"}),r=new ol.control.OverviewMap({className:"ol-overviewmap ol-custom-overviewmap",layers:[new ol.layer.Tile({source:new ol.source.BingMaps({key:"ApfTCVvx7xcxh2av-AYXGdCggwjy0THz6rlmJkWwt6ScjhxqnzBXlF6i7nJD_Eue",imagerySet:"AerialWithLabels"})})],view:n,collapseLabel:"»",label:"«",collapsed:!1}),o=[new ol.control.ScaleLine,new ol.control.FullScreen,r],i=ol.interaction.defaults({shiftDragZoom:!1,mouseWheelZoom:!1});return e.SPUViewer=function(t){this.settings=t,this.servers=t.servers,this.layers={},this.start=!1},e.SPUViewer.prototype.init=function(){this.settings.view?this.view=this.settings.view:this.view=new ol.View({projection:"EPSG:4326",center:[-51.4198237555309,-14.2364826698601],zoom:4.54});for(var t={},n=Object.keys(this.servers),a=0;a<n.length;a++){var s=n[a],c=this.servers[s],u=new e.InterfaceGeoserver(c.url,c.workspace);t[s]=u}this.servers=t,this.map=new ol.Map({target:this.settings.target,controls:o,interactions:i,view:this.view,logo:!1});var l=this;if(r){$(".ol-custom-overviewmap").attr("title","Mude a camada de visualização");var d=r.getOverviewMap();$(".ol-custom-overviewmap canvas").click(function(){var t=l.map.getLayers(),e=d.getLayers(),n=t.getArray()[0],r=e.getArray()[0];t.removeAt(0),e.removeAt(0),t.insertAt(0,r),e.insertAt(0,n)})}return this},e.SPUViewer.prototype.addLayer=function(t,e){return this.map.getLayers().remove(this.layers[t]),this.layers[t]=e,this.map.addLayer(this.layers[t]),e},e.SPUViewer.prototype.addLayerWMS=function(t,e,n,r,o,i){this.map.getLayers().remove(this.layers[e]);var a=this.servers[t],s=a.createLayerWms(e,n,r,o,i);return this.addLayer(e,s),s},e.SPUViewer.prototype.addLayerEmpty=function(t){this.map.getLayers().remove(this.layers[t]);var e=new ol.layer.Vector({source:new ol.source.Vector});return this.layers[t]=e,this.map.addLayer(this.layers[t]),e},e.SPUViewer.prototype.removeLayer=function(t){return this.layers[t]&&this.map.getLayers().remove(this.layers[t]),this},e.SPUViewer.prototype.setZoom=function(e){t.zoomByExtent(this.map,e)},e.SPUViewer.prototype.getMap=function(){return this.map},e.SPUViewer.prototype.getView=function(){return this.view},e.SPUViewer.prototype.getLayer=function(t){return this.layers[t]},e.SPUViewer.prototype.getLayers=function(){return this.layers},e.InterfaceGeoserver=function(t,e){this.workspace=e,this.url=t+"/"+this.workspace+"/";this.urlwfs=this.url+"ows?service=WFS&version=1.0.0&request=GetFeature"},e.InterfaceGeoserver.prototype.getUrlWms=function(){return this.url+"wms"},e.InterfaceGeoserver.prototype.createLayerWms=function(t,e,n,r,o){var i={LAYERS:t,EPSG:"4326",VERSION:"1.1.1"};o&&(i.cql_filter='"'+o+'"');var a={source:new ol.source.TileWMS({url:this.getUrlWms(),params:{LAYERS:t,EPSG:"4326",VERSION:"1.1.1"}}),visible:e};return n&&(a.minResolution=n),r&&(a.maxResolution=r),new ol.layer.Tile(a)},{sv:e}}])}(),function(){"use strict";function t(t,e){var n=t;n.toggleMenu=function(){e("left").toggle()},n.closeMenu=function(){e("left").close()}}t.$inject=["$scope","$mdSidenav"],angular.module("suApp").component("suNavbar",{templateUrl:"scripts-geoidentificacao/su/navbar.html",controller:t})}(),function(){"use strict";angular.module("su-geoidentificacao").factory("giSpuviewerFactory",["giOlFunctions",function(t){var e=new Object,n=new ol.View({projection:"EPSG:4326"}),r=new ol.control.OverviewMap({className:"ol-overviewmap ol-custom-overviewmap",layers:[new ol.layer.Tile({source:new ol.source.BingMaps({key:"ApfTCVvx7xcxh2av-AYXGdCggwjy0THz6rlmJkWwt6ScjhxqnzBXlF6i7nJD_Eue",imagerySet:"AerialWithLabels"})})],view:n,collapseLabel:"»",label:"«",collapsed:!1}),o=[new ol.control.ScaleLine,new ol.control.FullScreen,r],i=ol.interaction.defaults({shiftDragZoom:!1,mouseWheelZoom:!1});return e.SPUViewer=function(t){this.settings=t,this.servers=t.servers,this.layers={},this.start=!1},e.SPUViewer.prototype.init=function(){var t=this;this.settings.view?this.view=this.settings.view:this.view=new ol.View({projection:"EPSG:4326",center:[-51.4198237555309,-14.2364826698601],zoom:4.54});var n={};for(var a in this.servers){var s=this.servers[a],c=new e.InterfaceGeoserver(s.url,s.workspace);n[a]=c}if(this.servers=n,this.map=new ol.Map({target:this.settings.target,controls:o,interactions:i,view:this.view,logo:!1}),this.start=!0,r){$(".ol-custom-overviewmap").attr("title","Mude a camada de visualização");var u=r.getOverviewMap();$(".ol-custom-overviewmap canvas").click(function(){var e=t.map.getLayers(),n=u.getLayers(),r=e.getArray()[0],o=n.getArray()[0];e.removeAt(0),n.removeAt(0),e.insertAt(0,o),n.insertAt(0,r)})}return this},e.SPUViewer.prototype.addLayer=function(t,e){this.map.getLayers().remove(this.layers[t]),this.layers[t]=e,this.map.addLayer(this.layers[t])},e.SPUViewer.prototype.addLayerWMS=function(t,e,n,r,o){var i=this.servers[t],a=i.create_layer_wms(e,n,r,o);this.addLayer(e,a)},e.SPUViewer.prototype.addLayerWMSCQL=function(t,e,n,r,o,i){var a=this.servers[t],s=a.create_layer_wms_with_var(e,n,r,o,i);this.addLayer(e,s)},e.SPUViewer.prototype.removeLayer=function(t){this.layers[t]&&this.map.getLayers().remove(this.layers[t])},e.SPUViewer.prototype.setZoom=function(e){if("string"==typeof e){var n=e;"BOX"==n.substring(0,3)&&(e=t.extent2array(e)),"Env"==n.substring(0,3)&&(e=t.extent2arrayENV(e))}t.panAnimation(t.getCenterByExtent(e));var r=t.getResolutionForExtent(e,map.getSize());r=r,t.zoomAnimation(r)},e.SPUViewer.prototype.getMap=function(){return this.map},e.SPUViewer.prototype.getView=function(){return this.view},e.SPUViewer.prototype.getLayer=function(t){return this.layers[t]},e.InterfaceGeoserver=function(t,e){this.workspace=e,this.url=URL+"/ide/";this.url_wfs=this.url+"ows?service=WFS&version=1.0.0&request=GetFeature"},e.InterfaceGeoserver.prototype.get_url_wms=function(t){return this.url+"wms"},e.InterfaceGeoserver.prototype.create_layer_wms=function(t,e,n,r){var o={source:new ol.source.TileWMS({url:this.get_url_wms(),params:{LAYERS:t,EPSG:"4326",VERSION:"1.1.1"}}),visible:e};return null!=n&&(o.minResolution=n),null!=r&&(o.maxResolution=r),new ol.layer.Tile(o)},e.InterfaceGeoserver.prototype.create_layer_wms_with_var=function(t,e,n,r,o){var i={source:new ol.source.TileWMS({url:this.get_url_wms(),params:{LAYERS:t,EPSG:"4326",VERSION:"1.1.1",cql_filter:'"'+o+'"'}}),visible:e};return null!=n&&(i.minResolution=n),null!=r&&(i.maxResolution=r),new ol.layer.Tile(i)},{sv:e}}])}(),function(){"use strict";function t(t){return(new ol.format.WKT).readFeature(t,{dataProjection:"EPSG:4326",featureProjection:"EPSG:3857"}).getGeometry().getCoordinates()}angular.module("su-geoidentificacao").factory("giOlFunctions",function(){function e(){var t=txt.replace("BOX(","").replace(")","").replace(","," ").split(" ");for(var e in t)t[e]=parseFloat(t[e]);return t}function n(t){var e=t.replace("Env[","").replace("]","").replace(/ /g,"").replace(/:/g," ").replace(/,/," ").split(" "),n=[];for(var r in e)r%2==0&&n.push(parseFloat(e[r]));for(var r in e)r%2==1&&n.push(parseFloat(e[r]));return n}function r(t){return ol.extent.getCenter(t)}function o(t){var e=Number(new Date),n=ol.animation.pan({duration:f,source:map.getView().getCenter(),start:e});map.beforeRender(n),map.getView().setCenter(t)}function i(t,e){var n=ol.extent.getWidth(t)/e[0],r=ol.extent.getHeight(t)/e[1];return Math.max(n,r)}function r(t){return ol.extent.getCenter(t)}function a(t){var e=Number(new Date),n=ol.animation.zoom({duration:f,resolution:map.getView().getResolution(),start:e});map.beforeRender(n),map.getView().setResolution(t)}function s(){var t=document.createElement("canvas"),e=t.getContext("2d");t.width=6,t.height=6,e.fillStyle="rgb(0, 0, 0)";for(var n=0;n<6;++n)e.fillRect(n,n,1,1);return e.createPattern(t,"repeat")}function l(t){return(new ol.format.WKT).readFeature(t,{dataProjection:"EPSG:4326",featureProjection:"EPSG:3857"}).getGeometry().getArea()/1e6}function d(t){var e=new ol.format.WKT,n=e.readFeature(t),r=new ol.layer.Vector({source:new ol.source.Vector({features:[n]})});viewer.addLayer("draw",r),viewer.setZoom(n.getGeometry().getExtent())}function p(t){var r=[];return t.forEach(function(t){if("string"==typeof t){var o=t;"BOX"==o.substring(0,3)&&(t=e(t)),"Env"==o.substring(0,3)&&(t=n(t))}var i=t,a=[i[0],i[1]],s=[i[2],i[3]];r.push(a),r.push(s)}),ol.extent.boundingExtent(r)}var f=2e3;return{extent2array:e,extent2arrayENV:n,panAnimation:o,getCenterByExtent:r,getResolutionForExtent:i,zoomAnimation:a,extentFeaturesExtent:p,desenhaArquivo:d,estiloRachurado:c,estiloRachuradoVermelho:u,getAproxAreaFrom4326WktCoordinates:l,getCoordinatesFromGeometry:t,getHashedPattern:s}});var e=document.createElement("canvas"),n=ol.has.DEVICE_PIXEL_RATIO,r=e.getContext("2d"),o=function(){return e.width=11*n,e.height=11*n,r.moveTo(0,0),r.lineTo(300,150),r.stroke(),r.createPattern(e,"repeat")}(),i=new ol.style.Fill,a=new ol.style.Style({fill:i,stroke:new ol.style.Stroke({color:"#333",width:2})}),s=new ol.style.Style({fill:i,stroke:new ol.style.Stroke({color:"#D31010",width:2})}),c=function(t,e){return i.setColor(o),a},u=function(t,e){return i.setColor(o),s}}(),function(){"use strict";angular.module("su-geoidentificacao").factory("mapService",["$http","giConfig",function(t,e){function n(n){var r=new FormData;return r.append("file",n),t.post(e.geoservicosUrl+"/upload/uploadArquivosProcessoDemarcatorio",r,{transformRequest:angular.identity,headers:{"Content-Type":void 0}})}function r(n){var r=new FormData;return r.append("file",n),t.post(e.geoservicosUrl+"/upload/uploadArquivosSuspensao",r,{transformRequest:angular.identity,headers:{"Content-Type":void 0}})}function o(n){return t.post(e.geoservicosUrl+"/geoindentificacao/validarGeometriaBrasil",{geometria:n})}function i(n,r,o){return t.post(e.geoservicosUrl+"/transformacao/wktGeometry",{geometryWkt:n,sourceCoordinateSystem:r,destinyCoordinateSystem:o})}return{buscarLocalidadesPorCoordenadas:function(e){return t.get("https://nominatim.openstreetmap.org/search?q="+e+"&format=json&countrycodes=br&limit=5&polygon_geojson=1")},buscarMunicipiosInterceptantes:function(n){return t.post(e.apiUrl+"/geoinformacao/municipios-geo-coincidentes",n)},validarGeometriaDentroBrasil:o,uploadShapefile:n,uploadArquivosSuspensao:r,transformarWkt:i}}])}();var map,overlayPopUP,viewer,URL,typeSelect,draw,IS_DRAW=!1,source,vector;!function(){"use strict";angular.module("su-geoidentificacao").component("giMap",{bindings:{mviewer:"=",geometry:"=",mostrarBotaoUpload:"<",mostrarBotaoDesenhar:"<",onInitMap:"&"},transclude:!0,templateUrl:"scripts-geoidentificacao/map/map.html",controller:["$scope","mapService","giService","giSpuviewerFactory","$rootScope","giOlFunctions","$stateParams","$location","$filter",function(t,e,n,r,o,i,a,s,c){var u=t,l=o,d=this;u.searchQuery="",u.buscaInvalida=!1,u.popup={},u.popup.showPopup=!1,u.showPopupIntersecoesGeo=!1,u.mostrarBotaoUpload=!1,u.mostrarBotaoDesenhar=!1,u.municipios=[],u.geometria=null,t.$on("escolhaMunicipio",function(t,e){u.municipioEscolhido=e.show}),t.$on("showIncluirArquivo",function(t,e){u.isUpload=e.show}),t.$on("show"),t.$on("showLegenda",function(t,e){u.showLegenda=e.show}),this.$onChanges=function(t){t.mostrarBotaoUpload&&(u.mostrarBotaoUpload=t.mostrarBotaoUpload.currentValue),t.mostrarBotaoDesenhar&&(u.mostrarBotaoDesenhar=t.mostrarBotaoDesenhar.currentValue)},this.$onInit=function(){d.api={},d.api.limparMunicipios=function(){u.municipios=[]},u.mviewer=this.mviewer||{},n.appParametros.get().$promise.then(function(e){URL=e.URL_GEOSERVER_VETOR;var n=new ol.layer.Tile({source:new ol.source.OSM});viewer=new r.sv.SPUViewer({servers:{vetor:{url:URL,workspace:"ide"}},target:"map"}),u.mviewer.viewer=viewer,setTimeout(function(){viewer.init(),viewer.addLayer("camadaBase",n),viewer.addLayerWMS("vetor","uf",!0),viewer.addLayerWMS("vetor","municipio",!0,null,.01),viewer.addLayerWMS("vetor","tb_areas_uniao",!0),map=viewer.getMap(),l.viewer=viewer,source=new ol.source.Vector({wrapX:!1}),vector=new ol.layer.Vector({source:source}),viewer.addLayer("draw",vector),o.$broadcast("startedMap"),overlayPopUPIntersecoesGeo=new ol.Overlay({element:i,autoPan:!0,autoPanAnimation:{duration:250}}),map.addOverlay(overlayPopUPIntersecoesGeo);var t=document.getElementById("popup"),e=document.getElementById("popup-closer");overlayPopUP=new ol.Overlay({element:t,autoPan:!0,autoPanAnimation:{duration:250}}),e.onclick=function(){return overlayPopUP.setPosition(void 0),e.blur(),!1},map.addOverlay(overlayPopUP),viewer.getMap().updateSize(),map.getView().on("change:resolution",function(t){var e=!1;map.getView().getResolution()>t.oldValue&&(e=!0);var n=map.getView().calculateExtent(map.getSize());o.$broadcast("mapmoveend",{extent:n,zoomingOut:e})})},6e3);var i=document.getElementById("popupIntersecoes");document.getElementById("popup-contentIntersecoes"),document.getElementById("popup-closerIntersecoes");u.close=function(){return overlayPopUPIntersecoesGeo.setPosition(void 0),u.geometadados.produtoCartRadio=void 0,closer.blur(),!1},t.$on("popupConteudoIntersecoesGeo",function(t,e){u.popup=e.responsePopup,u.showPopupIntersecoesGeo=!0}),editar=!!s.path().includes("geoidentificacao/editar"),visualizar=!!s.path().includes("geoidentificacao/"+a.id),u.mapaCarregado=!0,d.onInitMap({$API:d.api}),d.geometry&&(u.geometria=d.geometry)})};var p=angular.element(document.body),f=new ol.interaction.MouseWheelZoom;p.bind("click",function(t){var e=self.viewer.getMap().getInteractions();"CANVAS"==t.target.nodeName?-1==e.getArray().indexOf(f)&&self.viewer.getMap().addInteraction(f):self.viewer.getMap().removeInteraction(f)}),u.drawMap=function(){source.clear(),IS_DRAW?(IS_DRAW=!1,map.removeInteraction(draw)):(IS_DRAW=!0,draw=new ol.interaction.Draw({source:source,type:"Polygon"}),map.addInteraction(draw)),draw.on("drawstart",function(){vector.getSource().clear(),u.geometria=null,l.geometria=null,viewer.getLayer("vectorCertos")&&viewer.getLayer("vectorCertos").getSource().clear(),viewer.getLayer("vectorIntercessoes")&&viewer.getLayer("vectorIntercessoes").getSource().clear(),viewer.getLayer("vectorErrados")&&viewer.getLayer("vectorErrados").getSource().clear(),viewer.getLayer("vectorForaPais")&&viewer.getLayer("vectorForaPais").getSource().clear()},this),draw.on("drawend",function(t){var n=new ol.format.WKT,r=t.feature,a=n.writeFeature(r);l.areaAproximada=i.getAproxAreaFrom4326WktCoordinates(a),u.geometria=a,l.geometria=a,map.removeInteraction(draw),e.validarGeometriaDentroBrasil(a).then(function(t){if(200===t.status){var e=t.data;if(200===e.status){e.data?o.$broadcast("geometriaDefinida",{tipo:"desenho",geometria:a,callback:u.buscarMunicipiosInterceptantes}):o.$broadcast("geometriaForaDoBrasil",{geometria:a,callback:u.buscarMunicipiosInterceptantes})}}})},this)},u.$on("geometriaEditada",function(){u.buscarMunicipiosInterceptantes()}),u.$on("geometriaAcrescentada",function(t,e){u.buscarMunicipiosInterceptantesAcrescentados(e)}),u.buscarMunicipiosInterceptantes=function(){var t=u.geometria;u.municipios=[],e.buscarMunicipiosInterceptantes(t).then(function(t){200===t.status&&t.data&&(u.municipios=t.data,o.$broadcast("municipiosDefinidos",u.municipios))}).catch(function(t){console.log("ERRO AO BUSCAR MUNICIPIOS",t)}).finally(function(){i.desenhaArquivo(t)})},u.buscarMunicipiosInterceptantesAcrescentados=function(t){e.buscarMunicipiosInterceptantes(t).then(function(t){200===t.status&&t.data&&(u.municipios=t.data,o.$broadcast("municipiosAcrescentadosDefinidos",u.municipios))}).catch(function(t){console.log("ERRO AO BUSCAR MUNICIPIOS",t)})},u.getListaUfsFormatados=function(t){if(!t)return"";for(var e=[],n=0;n<t.length;n++){var r=t[n];r&&r.uf&&e.push(r.uf.sgUf)}return e.length>0?e.join(", "):""},u.getListaMunicipiosFormatados=function(t){if(!t)return"";for(var e=[],n=0;n<t.length;n++){var r=t[n];r&&r.noAreaPoliticoAdministrativa&&e.push(r.noAreaPoliticoAdministrativa)}return e.length>0?e.join(", "):""},t.$on("popupConteudo",function(t,e){var r=e.responsePopup;u.popup.tipo=r.tipoDemarcacao.noTipoDemarcacao,u.popup.trecho=r.dsTrechoInicio&&r.dsTrechoFim?"inicial: "+r.dsTrechoInicio+"final: "+r.dsTrechoFim:r.dsTrecho,u.popup.portariaComissaoDemarcacao=r.portariaComissaoDemarcacao,u.popup.etapa=r.etapaDemarcacao.noEtapaDemarcacao,u.popup.situacao=void 0!=r.icSituacaoDemarcacao&&!0===r.icSituacaoDemarcacao?"Demarcado":"Não demarcado",u.popup.demandaGeradora=r.finalidadeDemarcacao.noFinalidadeDemarcacao,u.popup.ufs=u.getListaUfsFormatados(r.municipio),u.popup.municipios=u.getListaMunicipiosFormatados(r.municipio);var o=r.areaAproximada?r.areaAproximada/1e6:null;u.popup.areaAproximada=null!==o?o.toFixed(2):null,u.popup.nivelPrecisaoCartografia=r.niveisPrecisaoCartografica.dsNiveisPrecisaoCartografica,u.popup.nuProcessoDemarcatorio=r.nuProcessoDemarcatorio,u.popup.noCadastrador=r.noCadastrador,u.popup.dtCadastro=r.dtCadastro,u.popup.showPopup=!0,u.popup.municipios=[],u.popup.ufs=[],n.buscarMunicipiosPorGeometria(r.geomWkt).then(function(t){if(200===t.status){var e=t.data,n=new Set;angular.forEach(e,function(t){u.popup.municipios.push(t.nome),n.add(t.uf)}),u.popup.ufs=Array.from(n)}}).then(function(){var t=document.getElementById("ufs-popup-processo"),e=document.getElementById("municipios-popup-processo");t&&(t.innerHTML=u.popup.ufs.join(", ")),e&&(e.innerHTML=u.popup.municipios.join(", "))});var i="";u.popup.portariaComissaoDemarcacao&&u.popup.portariaComissaoDemarcacao.length&&u.popup.portariaComissaoDemarcacao.length>0&&(i="<p> <strong> Portaria de comissão de demarcação: </strong>",angular.forEach(u.popup.portariaComissaoDemarcacao,function(t){i+="<span>"+t.nuPortariaComissaoDemarcacao+"/"+t.anoPortariaComissaoDemarcacao+"<span> "}),i+="</p>");var a="",a="<p> <strong> Nº do Processo: </strong>"+u.popup.nuProcessoDemarcatorio+"</p><p> <strong> Tipo: </strong>"+(u.popup.tipo||"")+"</p><p> <strong> Trecho: </strong>"+(u.popup.trecho||"")+"</p>"+i+"<p> <strong> Etapa: </strong>"+(u.popup.etapa||"")+"</p><p> <strong> Situação: </strong>"+(u.popup.situacao||"")+"</p><p> <strong> Demanda geradora: </strong>"+(u.popup.demandaGeradora||"")+'</p><p> <strong> UF(s): </strong><span id="ufs-popup-processo"> </span></p><p> <strong> Municípios(s): </strong><span id="municipios-popup-processo">  </span></p><p> <strong> Área aproximada: </strong>'+(u.popup.areaAproximada||"")+"</p><p> <strong> Nível de precisão: </strong>"+(u.popup.nivelPrecisaoCartografia||"")+"</p><p> <strong> Cadastrador: </strong>"+(u.popup.noCadastrador||"")+"</p><p> <strong> Data de cadastro: </strong>"+(c("date")(u.popup.dtCadastro,"dd/MM/yyyy")||"")+"</p>";document.getElementById("popup-content").innerHTML=a,overlayPopUP.setPosition(e.coordinate)}),u.uploadFile=function(t){t&&e.uploadShapefile(t).then(function(t){if(200===t.status)if(t.data.data&&400===t.data.data.status)o.$broadcast("uploadfalhou",t.data.data.msg);else if(t.data&&200===t.data.status){var n=t.data.data.data;e.transformarWkt(n,4674,4326).then(function(e){if(200===e.status){var r=e.data.geometriaWkt;u.geometria=r,l.geometria=r,d.geometry=r,l.areaAproximada=i.getAproxAreaFrom4326WktCoordinates(n),o.$broadcast("geometriaDefinida",{geometria:r,arquivo:t.data.data.byteArray,noArquivo:t.data.data.nome,callback:u.buscarMunicipiosInterceptantes})}else console.error(t.data)})}else o.$broadcast("uploadFalhou","Erro ao enviar arquivo.");else o.$broadcast("uploadFalhou","Erro ao enviar arquivo.")}).catch(function(t){o.$broadcast("uploadfalhou","Erro ao enviar arquivo")})},u.executeQuery=function(){""!==u.searchQuery&&e.buscarLocalidadesPorCoordenadas(u.searchQuery).then(function(t){var e,n=t.data;if(200===t.status&&t.data.length>0){for(var r=0;r<n.length-1;r++)if("Polygon"==n[r].geojson.type){e=n[r];break}var o=e.geojson.coordinates,i=new ol.geom.Polygon(o);u.mviewer.viewer.setZoom(i.getExtent())}}).finally(function(){u.searchQuery=""})}}]})}(),function(){"use strict";function t(t,e,n,r,o,i,a,s,c,u){function l(){var t=new ol.layer.Vector({source:new ol.source.Vector});m.viewer.addLayer(v,t)}function d(){var t=m.geoinformacao,e=_.readFeature(t.geomWkt);e.setId(t.idProcessoDemarcatorio);var n=t.tipoDemarcacao.noTipoDemarcacao,r=null;r="Terreno de Marinha"===n?m.tiposAreasCadastradas.MARINHA:"Terreno Marginal"===n?m.tiposAreasCadastradas.MARGINAL:m.tiposAreasCadastradas.OUTRO;var o=m.estiloAreasCadastradasPorTipo[r];if(e.setStyle(o.estiloFeature),m.viewer.getLayer(v).getSource().addFeature(e),m.viewer.getView().fit(e.getGeometry().getExtent(),m.viewer.getMap().getSize()),t.icSituacaoDemarcacao&&!1===t.icSituacaoDemarcacao){var i=e.clone();i.setStyle(m.estiloAreasCadastradasPorTipo.naoDemarcado.estiloFeature),m.viewer.getLayer(v).getSource().addFeature(i)}m.controleLegenda[r]=!0}function p(){r.buscarAreaAcrescidaPorProcessoDemarcatorio(m.geoinformacao.idProcessoDemarcatorio).then(function(t){if(200===t.status){var e=t.data;if(e){m.areaAcrescida=e;var n=_.readFeature(e.geomWktAcrescida);n.setId(m.areaAcrescida.id);var r=null;r=m.geoinformacao.coTipoDemarcacao==m.tiposDemarcacao.MARINHA?m.tiposAreasCadastradas.ACRESCIDO_MARINHA:m.tiposAreasCadastradas.ACRESCIDO_MARGINAL;var o=m.geoinformacao.coTipoDemarcacao===m.tiposDemarcacao.MARINHA?"#aad08e61":"#f8cbad5c",i=m.geoinformacao.coTipoDemarcacao===m.tiposDemarcacao.MARINHA?"#AAD08E":"#F8CBAD";n.setStyle(new ol.style.Style({fill:new ol.style.Fill({color:o}),stroke:new ol.style.Stroke({color:i})})),m.viewer.getLayer(v).getSource().addFeature(n),m.controleLegenda[r]=!0}}else console.error("Erro ao buscar arquivo acrescido")})}function f(){r.buscarAreasSuspensasPorProcessoDemarcatorio(m.geoinformacao.idProcessoDemarcatorio).then(function(t){if(200===t.status){var e=t.data;if(e&&e.length)for(var n=0;n<e.length;n++){var r=e[n];if(r&&r.id){m.areasSuspensas.push(r),r.tbProcessoDemarcatorioSuspensaoArquivos&&r.tbProcessoDemarcatorioSuspensaoArquivos.length&&r.tbProcessoDemarcatorioSuspensaoArquivos.length>0&&(r.documentoSuspensaoMetadados=r.tbProcessoDemarcatorioSuspensaoArquivos[0],"PDF"===r.documentoSuspensaoMetadados.tipoArquivo&&(r.documentoSuspensaoMetadados.href=b(r.documentoSuspensaoMetadados)));var o=_.readFeature(r.geomWkt);o.setId(r.id);var i=m.estiloAreasCadastradasPorTipo.suspenso;o.setStyle(i.estiloFeature),m.viewer.getLayer(v).getSource().addFeature(o),m.controleLegenda.suspenso=!0}}}else console.error("Falha ao adicionar suspensões")})}var h,g,m=t,y=e;m.geoserverUrl=o.geoserverUrl,m.mviewer={},m.mostrarQuery=!1,m.estiloAreasCadastradasPorTipo={marinha:{cor:"#548235",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(84, 130, 53, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(84, 130, 53, 1)"})})},acrescidoMarinha:{cor:"#AAD08E",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(170, 208, 142, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(170, 208, 142, 1)"})})},marginal:{cor:"#FFD966",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(255, 217, 102, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(255, 217, 102, 1)"})})},acrescidoMarginal:{cor:"#F8CBAD",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(248, 203, 173, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(248, 203, 173, 1)"})})},suspenso:{cor:"#FF0000",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(0, 0, 0, 0)"}),stroke:new ol.style.Stroke({color:"rgba(255, 0, 0, 1)"})})},sobreposta:{cor:"#6600CC",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(102, 0, 204, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(102, 0, 204, 1)"})})},invalido:{estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(255, 0, 0, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(255, 0, 0, 1)"})})},outro:{cor:"#666666",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(102, 102, 102, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(102, 102, 102, 1)"})})},naoDemarcado:{estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:s.getHashedPattern()}),stroke:new ol.style.Stroke({color:"rgba(0, 0, 0, 1)"})})}},m.tiposAreasCadastradas={MARINHA:"marinha",ACRESCIDO_MARINHA:"acrescidoMarinha",MARGINAL:"marginal",ACRESCIDO_MARGINAL:"acrescidoMarginal",SUSPENSO:"suspenso",SOPREPOSTA:"sobreposta",OUTRO:"outro",NOVO:"novo",INVALIDO:"invalido",PRODUTOS_CARTOGRAFICO:"produtoCartografico",SOBREPOSTA_PRODUTOS_CARTOGRAFICOS:"sobrepostaProdutosCartograficos"},m.tiposDemarcacao={MARINHA:1,MARGINAL:2},m.controleLegenda={marinha:!1,acrescidoMarinha:!1,marginal:!1,acrescidoMarginal:!1,suspenso:!1,sobreposta:!1,outro:!1,invalido:!1,produtoCartografico:!1,sobrepostaProdutosCartograficos:!1},m.areasSuspensas=[],t.$on("mapaIniciado",function(){m.viewer=m.mviewer.viewer})
;var v="camadaDeAreas",_=new ol.format.WKT,b=function(t){for(var e=window.atob(t.arquivo),n=e.length,r=new Int8Array(n),o=0;o<n;o++)r[o]=e.charCodeAt(o);var i=new Blob([r],{type:"application/pdf"});return window.URL.createObjectURL(i)};m.mostrarInfoSuspensoes=function(){return void 0!==m.areasSuspensas&&null!==m.areasSuspensas&&m.areasSuspensas.length&&m.areasSuspensas.length>0},m.mostrarInfoTerrenoAcrescido=function(){return void 0!==m.areaAcrescida&&null!==m.areaAcrescida},m.adicionarAreasAoMapa=function(){l(),d(),p(),f()},a.usuarioLogado(function(){function t(t,e){m.query={},m.query.geomWKT=t,m.query.idProcesso=e,r.geometadadosPorProcesso.save(m.query,function(t){m.total=t.totalElements;m.geometadados=t.content,h=t.content;var e=[],n=new ol.format.WKT;h.length>0?m.showCheckVisualizarProdCartGeom=!0:m.showCheckVisualizarProdCartGeom=!1,angular.forEach(t.content,function(t,r){if(t&&null!=t.geomRepresentacaoEspacialWKT){var o=new ol.Feature({geometry:n.readFeature(t.geomRepresentacaoEspacialWKT).getGeometry()});o.setId(t.idProdutoCartografico),e.push(o)}});var r=new ol.layer.Vector({source:new ol.source.Vector({features:e}),style:s.estiloRachurado});setTimeout(function(){m.mviewer.viewer.addLayer("vectorMetadadosIntercessoes",r),g=m.mviewer.viewer.getLayer("vectorMetadadosIntercessoes"),g.setVisible(!1),o()},2e3)})}function o(){var t=m.mviewer.viewer.getMap();t.getViewport().oncontextmenu=null,t.getViewport().oncontextmenu=function(n){var o=[];o.push(n.layerX),o.push(n.layerY);var i=t.getCoordinateFromPixel(o),a=t.forEachFeatureAtPixel(o,function(t){return t}),s=a.getId();if(a&&void 0!=a.getId()){var c=m.geometria;m.query.geomWKT=c,r.produtoCartografico.get({id:s},function(t){e.$broadcast("popupConteudoIntersecoesGeo",{responsePopup:t}),overlayPopUPIntersecoesGeo.setPosition(i)})}return!1}}m.authenticatedEditar=-1!==y.usuario.permissoes.indexOf(a.editar),m.alertPermissao=function(t){a.alertPermissao(t)},r.geoinformacao.get({id:n.id},function(e){m.geoinformacao=e;var n=[];if(m.geoinformacao.portariaComissaoDemarcacao&&m.geoinformacao.portariaComissaoDemarcacao.length){for(var o=0;o<m.geoinformacao.portariaComissaoDemarcacao.length;o++){var i=m.geoinformacao.portariaComissaoDemarcacao[o],a=i.nuPortariaComissaoDemarcacao+"/"+i.anoPortariaComissaoDemarcacao;n.push(a)}m.adicionarAreasAoMapa()}!0===m.geoinformacao.icSituacaoDemarcacao?m.geoinformacao.situacao="Demarcado":m.geoinformacao.situacao="Não Demarcado",m.geoinformacao.coProdutosCartograficosRelacionados.length&&m.geoinformacao.coProdutosCartograficosRelacionados.length>0?m.geoinformacao.produtosRelacionados="Sim":m.geoinformacao.produtosRelacionados="Não",m.metrosParaKilometrosQuadrados=function(t){return(t/1e6).toFixed(2)},r.buscarMunicipiosPorGeometria(m.geoinformacao.geomWkt).then(function(t){var e=new Set,n=[];if(200===t.status){for(var r=t.data,o=0;o<r.length;o++){var i=r[o];n.push(i.nome),e.add(i.uf)}var a=[];angular.forEach(Array.from(e.values()),function(t){a.push(t)}),m.municipiosFormatados=n.join(", "),m.ufsFormatados=a.join(", ")}else console.debug("Erro ao buscar municipios: ",t.data)}),m.portarias=n.join(", "),t(e.geomWkt,e.idProcessoDemarcatorio),m.showMotivo=function(){return e.icSituacaoDemarcacao?(m.geoinformacao.dsSituacaoDemarcacao="Ativa",!1):(m.geoinformacao.dsSituacaoDemarcacao="Suspensa",!0)},null!==m.geoinformacao.nuProcessoDemarcatorio&&""!==m.geoinformacao.nuProcessoDemarcatorio&&void 0!==m.geoinformacao.nuProcessoDemarcatorio&&(null!==m.geoinformacao.dsJustificativaGeometadados&&void 0!==m.geoinformacao.dsJustificativaGeometadados&&""!==m.geoinformacao.dsJustificativaGeometadados?m.showLabelCartAssociada=!1:r.geometadadosTituloProdCart.query({numProcesso:m.geoinformacao.idProcessoDemarcatorio},function(t){m.showLabelCartAssociada=!0,m.geoinformacao.listCartografiaAssociada=t}))}),m.visibilidadeGeomProdCart=function(t){g.setVisible(t)},m.voltar=function(){c.path("/geoidentificacao"),setTimeout(function(){u.location.reload()},2e3)}})}t.$inject=["$scope","$rootScope","$stateParams","giService","giConfig","olUtils","giUsuario","giOlFunctions","$location","$window"],angular.module("su-geoidentificacao").component("geoidentificacaovisualizar",{templateUrl:"scripts-geoidentificacao/geoidentificacao/geoidentificacao_visualizar.html",controller:t})}(),function(){function t(t,e,n,r,o,i,a,s,c){function u(t){return{formatDate:function(e){return e?moment(e).format(t):null},parseDate:function(e){if(e){var n=moment(e,t,!0);return n.isValid()?n.toDate():new Date(NaN)}return null}}}function l(){n.listarTiposSuspensoes.get({coEtapa:y.geoidentificacao.etapaDemarcacao.idEtapaDemarcacao,icSituacao:y.geoidentificacao.icSituacaoDemarcacao},function(t){y.tipoSuspensoes=t.data,y.idGeoidentificacaoSuspensao?d():y.geoidentificacaoSuspensao.tipoSuspensao={idTipoSuspensao:void 0}})}function d(){n.getSuspensao.get({id:y.idGeoidentificacaoSuspensao},function(t){y.geoidentificacaoSuspensao=t,y.geoidentificacaoSuspensao.dtInicio=new Date(y.geoidentificacaoSuspensao.dtInicio),y.geoidentificacaoSuspensao.dtFim&&(y.geoidentificacaoSuspensao.dtFim=new Date(y.geoidentificacaoSuspensao.dtFim)),p()})}function p(){y.paginationOptions={idSuspensao:y.idGeoidentificacaoSuspensao,page:0,size:5},n.pageSuspensaoArquivos.get(y.paginationOptions,function(t){y.geoidentificacaoSuspensaoDocumentos=t,y.paginationOptions.page++})}function f(t){1!==t&&2!==t&&3!==t||(y.mostrarMotivo=!0)}function h(t){if(t){var n=e.alert().multiple(!0).clickOutsideToClose(!1).textContent(t).ok("OK");e.show(n)}}function g(t){return e.confirm().multiple(!0).title(t).ok("Sim").cancel("Não")}function m(t){var e=c.getCoordinatesFromGeometry(y.geometriaAreaUniao)[0],n=c.getCoordinatesFromGeometry(t),r=turf.polygon([e[0]]);return _.every(n[0],function(t){return turf.booleanPointInPolygon(t,r)})}var y=t;y.geoidentificacao=r,y.idGeoidentificacaoSuspensao=o,y.geometriaAreaUniao=i,y.isVisualizar=a,y.tipoSuspensoes=[],y.mostrarMotivo=!1,y.geoidentificacaoSuspensao={},y.geoidentificacaoSuspensaoDocumentos={},y.geoidentificacaoSuspensaoDocumentos.content=[],y.nuProcessoSEI=void 0,y.bytteArrayPDF={},y.monthFormat=u("DD/MM/YYYY"),y.cancelar=function(){e.hide()},y.limpar=function(){y.geoidentificacaoSuspensao.tipoSuspensao=void 0,y.geoidentificacaoSuspensao.motivo=void 0,y.geoidentificacaoSuspensao.tipoDocumento=void 0,y.geoidentificacaoSuspensao.arquivo=void 0,y.geoidentificacaoSuspensao.noArquivo=void 0,y.geoidentificacaoSuspensao.tipoSuspensao=void 0,y.bytteArrayPDF=void 0,y.geoidentificacaoSuspensao.nuProcessoSEI=void 0,y.geoidentificacaoSuspensaoDocumentos=void 0,y.geoidentificacaoSuspensao.areaAproximada=void 0,y.geoidentificacaoSuspensao.areaOficial=void 0,y.geoidentificacaoSuspensao.dtInicio=void 0,y.geoidentificacaoSuspensao.dtFim=void 0},y.salvar=function(){y.form.$valid&&y.geoidentificacaoSuspensaoDocumentos&&y.geoidentificacaoSuspensaoDocumentos.content.length&&y.geoidentificacaoSuspensao.arquivo?(y.geoidentificacaoSuspensao.idProcessoDemarcatorio=y.geoidentificacao.idProcessoDemarcatorio,y.geoidentificacaoSuspensao.tbProcessoDemarcatorioSuspensaoArquivos=y.geoidentificacaoSuspensaoDocumentos.content,n.gravarSuspensao.save(y.geoidentificacaoSuspensao,function(t){e.hide(t)})):h("Necessário informar os campos obrigatórios")},y.fileChanged=function(t){t&&s.uploadArquivosSuspensao(t).then(function(t){200===t.status&&(200===t.data.status?(y.bytteArrayPDF.byttes=t.data.byteArray,y.bytteArrayPDF.name=t.data.nome):h(t.data.msg))}).catch(function(t){h("Erro no upload do arquivo")})},y.consultaDocumentoSei=function(){y.geoidentificacaoSuspensao.nuProcessoSEI?n.consultaDocumentoSei.get({documento:y.geoidentificacaoSuspensao.nuProcessoSEI},function(t){null===t.idProcedimento?h("Nº do documento não encontrado"):(y.processoSEI=t,y.adicionarDocumento())},function(t){console.log(t),h("Nº do documento não encontrado.")}):h("Necessário informar um documento")},y.adicionarDocumento=function(){y.bytteArrayPDF&&y.bytteArrayPDF.byttes||y.processoSEI&&y.processoSEI.documentoFormatado?(y.geoidentificacaoSuspensaoDocumentos.content.unshift({arquivo:y.bytteArrayPDF.byttes?y.bytteArrayPDF.byttes:void 0,noArquivo:y.bytteArrayPDF.byttes?y.bytteArrayPDF.name:void 0,nuProcessoSEI:y.processoSEI?y.processoSEI.documentoFormatado:void 0,linkProcessoSEI:y.processoSEI?y.processoSEI.linkAcesso:void 0,tipoArquivo:y.bytteArrayPDF.byttes?"PDF":"PROCESSO_SEI"}),y.bytteArrayPDF={},y.processoSEI={},y.geoidentificacaoSuspensao.nuProcessoSEI=void 0):h("Necessário informar um documento")},y.limparDocumento=function(){y.bytteArrayPDF=void 0,y.geoidentificacaoSuspensao.nuProcessoSEI=void 0},y.download=function(t,e,n){if(t&&e&&n){var r=window.document.createElement("a");r.href=t,r.href="data:application/"+n+";base64,"+t,r.download=e,document.body.appendChild(r),r.click(),document.body.removeChild(r)}},y.removerDocumento=function(t,r){e.show(g("Deseja remover este documento?")).then(function(){y.geoidentificacaoSuspensaoDocumentos.content.splice(r,1),t.id&&n.excluirSuspensaoArquivo.delete({id:t.id})})},y.uploadFile=function(t){t&&s.uploadShapefile(t).then(function(t){200===t.status&&(t.data.data&&400===t.data.data.status?h(t.data.data.msg):t.data&&200===t.data.status?m(t.data.data.data)?(y.geoidentificacaoSuspensao.geomWkt=t.data.data.data,y.geoidentificacaoSuspensao.arquivo=t.data.data.byteArray,y.geoidentificacaoSuspensao.noArquivo=t.data.data.nome,y.geoidentificacaoSuspensao.areaAproximada=c.getAproxAreaFrom4326WktCoordinates(i)):h("Área suspensa inválida. O polígono da área suspensa deve estar contido no polígono da Área da União. Favor informar uma nova geometria de suspensão."):h("Erro ao enviar arquivo"))})},y.verificarDivergenciaDeArea=function(){if(y.geoidentificacaoSuspensao.areaAproximada){Math.abs(Math.floor(y.geoidentificacaoSuspensao.areaAproximada/y.geoidentificacaoSuspensao.areaOficial*100)-100)>10&&e.show(g("A Área Oficial informada é muito diferente da área do polígono demarcado. Deseja continuar?")).then(function(){},function(){y.geoidentificacaoSuspensao.areaOficial=null})}},y.filtrar=function(){y.paginationOptions.page--,n.pageSuspensaoArquivos.get(y.paginationOptions,function(t){y.geoidentificacaoSuspensaoDocumentos=t,y.paginationOptions.page++})},y.verificarDataFim=function(){y.geoidentificacaoSuspensao.dtFim&&y.geoidentificacaoSuspensao.dtFim<y.geoidentificacaoSuspensao.dtInicio&&(h("A Data do Fim da Suspensão não pode ser anterior à Data de Início da Suspensão."),y.geoidentificacaoSuspensao.dtFim=void 0)},function(){y.geoidentificacao&&(f(y.geoidentificacao.etapaDemarcacao.idEtapaDemarcacao),l(),u())}()}angular.module("su-geoidentificacao").controller("GeoidentificacaoSuspenderController",t),t.$inject=["$scope","$mdDialog","giService","geoidentificacao","idGeoidentificacaoSuspensao","geometria","isVisualizar","mapService","giOlFunctions"]}(),function(){"use strict";function t(t,e,n,r,o,i){var a=t;a.total=0,a.tabelaHistorico=[],a.idGeoidentificacao=n.id,a.loading={mode:"query",disabled:!1},a.query={size:10,page:1,id:n.id},e.geoinformacao.get({id:n.id},function(t){a.geoidentificacao=t,e.buscarMunicipiosPorGeometria(a.geoidentificacao.geomWkt).then(function(t){var e=new Set,n=[];if(200===t.status){for(var r=t.data,o=0;o<r.length;o++){var i=r[o];n.push(i.nome),e.add(i.uf)}var s=[];angular.forEach(Array.from(e.values()),function(t){this.push(t)},s),a.municipiosFormatados=n.join(", "),a.ufsFormatados=s.join(", ")}else console.debug("Erro ao buscar municipios: ",t.data)}),a.buscaHistorico()}),a.buscaHistorico=function(){a.loading.mode="indeterminate",a.loading.disabled=!1,e.buscarHistorico.query({id:a.idGeoidentificacao},function(t){a.historicos=t,a.loading.disabled=!0,0===t.length&&r.show({position:"bottom right",template:'<md-toast><div class="md-toast-content">Nenhum registro encontrado</div></md-toast>'})})},a.voltar=function(){o.path("/geoidentificacao"),setTimeout(function(){i.location.reload()},2e3)},a.visualizar=function(t){o.path("/geoidentificacao/"+t),setTimeout(function(){i.location.reload()},2e3)}}t.$inject=["$scope","giService","$stateParams","$mdToast","$location","$window"],angular.module("su-geoidentificacao").component("geoidentificacaoHistorico",{templateUrl:"scripts-geoidentificacao/geoidentificacao/geoidentificacao_historico.html",controller:t})}(),function(){"use strict";function t(t,e,n,r,o,i,a,s,c,u,l,d){function p(){map.getViewport().oncontextmenu=null,map.getViewport().oncontextmenu=function(t){var r=[];r.push(t.layerX),r.push(t.layerY);var o=map.getCoordinateFromPixel(r),i=map.forEachFeatureAtPixel(r,function(t){return t});if(idG=i.getId(),i&&void 0!=i.getId()){var a=P.geometria;P.query.geomWKT=a,n.produtoCartografico.get({id:idG},function(t){e.$broadcast("popupConteudoIntersecoesGeo",{responsePopup:t}),overlayPopUPIntersecoesGeo.setPosition(o)})}return!1}}function f(){map.getViewport().oncontextmenu=null,map.getViewport().oncontextmenu=function(t){var n=[];n.push(t.layerX),n.push(t.layerY);var r=map.getCoordinateFromPixel(n),o=map.forEachFeatureAtPixel(n,function(t){return t});if(o&&void 0!==o.get("tipoProcesso")&&"regular"===o.get("tipoProcesso")){var i=o.get("processo");e.$broadcast("popupConteudo",{responsePopup:i,coordinate:r})}return!1}}function h(t,e){var n=document.getElementById(String(t.id));n&&n.remove();var r=document.createElement("div");r.id=t.id,r.className="tooltip",r.innerHTML="<div><b>Suspensão<b></div><div><b>Tipo<b/>&nbsp;:"+t.tipoSuspensao.noTipoSuspensao+"</div><div><b>Área<b/>&nbsp;:"+t.areaOficial+"</div><div><b>Período<b/>:&nbsp;"+l("date")(t.dtInicio,"dd/MM/yyyy")+(t.dtFim?" - "+l("date")(t.dtFim,"dd/MM/yyyy"):"")+"</div>";var o=new ol.Overlay({element:r});if(map.addOverlay(o),e){var i=g();o.setPositioning(i),o.setPosition([e.getGeometry().getExtent()[0],e.getGeometry().getExtent()[1]])}}function g(){var t=["bottom","center","top"],e=t[Math.floor(Math.random()*t.length)],n=["left","center","right"];return e+"-"+n[Math.floor(Math.random()*n.length)]}function m(){for(var t=(P.mviewer.viewer,0);t<P.produtosCartograficos.data.length;t++){var e=P.geoidentificacao.coProdutosCartograficosRelacionados.filter(function(e){return P.produtosCartograficos.data[t].id===e});e&&e.length&&(P.isProdutosCartograficos[t]=!0,y(e[0]),_(e[0]))}}function y(t){viewer.getLayer("vectorProdutosCartograficos").getSource().getFeatureById(t)&&viewer.getLayer("vectorProdutosCartograficos").getSource().getFeatureById(t).setStyle(new ol.style.Style({fill:new ol.style.Fill({color:"rgba(128,128,128, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(128,128,128, 1)"})}))}function v(t){viewer.getLayer("vectorProdutosCartograficos").getSource().getFeatureById(t)&&viewer.getLayer("vectorProdutosCartograficos").getSource().getFeatureById(t).setStyle(new ol.style.Style({}))}function _(t){viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().getFeatureById(t)&&viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().getFeatureById(t).setStyle(new ol.style.Style({fill:new ol.style.Fill({color:"rgba(102, 0, 204, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(102, 0, 204, 1)"})}))}function b(t){viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().getFeatureById(t)&&viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().getFeatureById(t).setStyle(new ol.style.Style({}))}function w(t){if(P.arquivosAcrescidos.content.length&&!x(t,P.arquivosAcrescidos.content[0].geomWktAcrescida)){var r=c.alert().clickOutsideToClose(!1).textContent("O polígono adicionado não é adjacente ao terreo acrescido").ok("OK");c.show(r);var o=P.areasCadastradas.processosDemarcatorios.map(function(t){return t.geomWkt});return void P.buscarSobreposicoesPorGeometria(P.geometria,o)}P.paginacaoSuspensao={idProcessoDemarcatorio:P.geoidentificacao.idProcessoDemarcatorio,isRemovido:!1,page:0,size:1e3},n.pageSuspensao.get(P.paginacaoSuspensao,function(n){if(n.content&&n.content.length){for(var r=!0,o=0;o<n.content.length;o++){var i=n.content[o];if(!E(t,i.geomWkt)){r=!1;break}}if(!r){var a=c.alert().clickOutsideToClose(!1).textContent("Os polígonos suspensos cadastrados não estão contidos dentro do novo polígono.").ok("OK");c.show(a);var s=P.areasCadastradas.processosDemarcatorios.map(function(t){return t.geomWkt});return void P.buscarSobreposicoesPorGeometria(P.geometria,s)}}P.validarGeometria(t),P.geoidentificacao.coProdutosCartograficosRelacionados=[],P.geoidentificacao.produtoCartRadio=void 0,P.produtosCartograficos={},P.limparAreasCadastradas(),P.buscarAreasCadastradas(t,!1),P.buscarProdutos("true",!1),P.isNovaGeometria=!0,P.legendaSuspenso=!1,e.$broadcast("geometriaEditada")})}function x(t,e){var n=a.getCoordinatesFromGeometry(t),r=a.getCoordinatesFromGeometry(e),o=turf.polygon([n[0]]),i=turf.polygon([r[0]]);return turf.intersect(o,i)}function E(t,e){var n=a.getCoordinatesFromGeometry(t),r=a.getCoordinatesFromGeometry(e),o=turf.polygon([n[0]]),i=turf.polygon([r[0]]);return turf.booleanWithin(i,o)}function C(){P.limparLegendaAreasCadastradas(),P.limparLegendaAreasValidadas(),P.buscarAreasCadastradas(P.geometria,!1)}var S,I,P=t,A=e,N=!0;P.mostrarBotaoDesenhar=!0,P.mostrarBotaoUpload=!0,P.mviewer={},P.trochoIgualFim=!1,P.verTerrenosRemovidos=!1,P.geometriaAcrescida=void 0,P.isPortariaAlterada=!0,P.arquivosAcrescidos={content:[]},P.monthFormat=function(t){return{formatDate:function(e){return e?moment(e).format(t):null},parseDate:function(e){if(e){var n=moment(e,t,!0);return n.isValid()?n.toDate():new Date(NaN)}return null}}}("YYYY"),P.maxDate=new Date(new Date),P.isProdutosCartograficos=[],P.tiposDemarcacao={MARINHA:1,MARGINAL:2},P.authenticatedEditar=!1,i.usuarioLogado();var M=function(t,e,n){for(var r=t.length;r--;)t[r]&&t[r].hasOwnProperty(e)&&arguments.length>2&&t[r][e]===n&&t.splice(r,1);return t};i.usuarioLogado(function(){function l(t,e,n){y=c.confirm().title(t).textContent(e).ok("SIM").cancel("NÃO"),c.show(y).then(function(){return P.geometria=n,!0},function(){return P.geometria=null,!1})}function d(t){var e=!0;return angular.forEach(t.valid,function(t){"anoPortariaComissaoDemarcacao"===t.$name||(e=!1)}),e}function h(){var t=P.geoidentificacao.coTipoDemarcacao,n=P.geoidentificacao.coEtapaDemarcacao;null!=t&&null!=n?(e.$broadcast("showVetorizarGeometria",{show:!0}),e.$broadcast("showIncluirArquivo",{show:!0})):(e.$broadcast("showVetorizarGeometria",{show:!1}),e.$broadcast("showIncluirArquivo",{show:!1})),P.atualizarSituacao(P.geoidentificacao.coEtapaDemarcacao)}if(A.selected=[],A.toggle=function(t){var e=A.selected,n=e.indexOf(t);n>-1?(e.splice(n,1),P.mviewer.viewer.getLayer("vectorMetadadosIntercessoes").getSource().getFeatureById(t).setStyle(a.estiloRachurado)):(e.push(t),P.mviewer.viewer.getLayer("vectorMetadadosIntercessoes").getSource().getFeatureById(t).setStyle(a.estiloRachuradoVermelho))},P.toggle=A.toggle,A.exists=function(t){return A.selected.indexOf(t)>-1},P.exists=A.exists,P.showMensagemGeoMetadados=!1,P.mensagemGeometadados="",-1!==A.usuario.permissoes.indexOf(i.editar)){P.authenticatedEditar=!0,P.ufsSelecionadas=[],P.municipiosSelecionados=[],P.listEtapaGeoidentificacao=[],P.mostrarLegenda=!1,P.estiloAreasCadastradasPorTipo={marinha:{cor:"#548235",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(84, 130, 53, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(84, 130, 53, 1)"})})},acrescidoMarinha:{cor:"#AAD08E",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(170, 208, 142, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(170, 208, 142, 1)"})})},marginal:{cor:"#FFD966",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(255, 217, 102, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(255, 217, 102, 1)"})})},acrescidoMarginal:{cor:"#F8CBAD",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(248, 203, 173, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(248, 203, 173, 1)"})})},suspenso:{cor:"#FF0000",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(0, 0, 0,)"}),stroke:new ol.style.Stroke({color:"rgba(255, 0, 0, 1)"})})},sobreposta:{cor:"#6600CC",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(102, 0, 204, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(102, 0, 204, 1)"})})},naoDemarcado:{estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:a.getHashedPattern()}),stroke:new ol.style.Stroke({color:"rgba(0, 0, 0, 1)"})})},invalido:{estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(255, 0, 0, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(255, 0, 0, 1)"})})},outro:{cor:"#666666",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(102, 102, 102, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(102, 102, 102, 1)"})})}},P.controleLegenda={marinha:!1,acrescidoMarinha:!1,marginal:!1,acrescidoMarginal:!1,suspenso:!1,sobreposta:!1,outro:!1,invalido:!1,produtoCartografico:!1,sobrepostaProdutosCartograficos:!1,naoDemarcado:!1},P.tiposAreasCadastradas={MARINHA:"marinha",ACRESCIDO_MARINHA:"acrescidoMarinha",MARGINAL:"marginal",ACRESCIDO_MARGINAL:"acrescidoMarginal",SUSPENSO:"suspenso",SOPREPOSTA:"sobreposta",OUTRO:"outro",NOVO:"novo",INVALIDO:"invalido",PRODUTOS_CARTOGRAFICO:"produtoCartografico",SOBREPOSTA_PRODUTOS_CARTOGRAFICOS:"sobrepostaProdutosCartograficos"},P.adicionarAreaCadastrada=function(t,e,n){P.controleLegenda[e]=!0,"produtoCartografico"===e?e="outro":"sobrepostaProdutosCartograficos"===e&&(e="sobreposta");var r=P.estiloAreasCadastradasPorTipo[e].estiloFeature;t.setStyle(r),viewer.getLayer(n).getSource().addFeature(t)};var g=c.alert().clickOutsideToClose(!1).textContent().ok("OK");P.clearAlert=function(t){t.textContent(""),t.title("")};var y;P.geoidentificacao={},n.finalidadeGeoidentificacao.get(function(t){P.listFinalidadeGeoidentificacao=t.data}),n.tipoGeoidentificacao.get(function(t){P.listTipoGeoidentificacao=t.data}),n.etapaGeoidentificacao.get(function(t){P.listEtapaGeoidentificacao=t.data}),n.niveisPrecisaoCartograficasService.get(function(t){P.niveisPrecisaoCartografica=t.data});var v=0;P.finished=function(){++v===P.ufsSelecionadas.length&&setTimeout(function(){N=!1},2e3)},P.obterTipoAreaCadastrada=function(t,e){var n=null;if(e)switch(e){case"regular":var r=t.tipoDemarcacao.noTipoDemarcacao;n="Terreno de Marinha"===r?P.tiposAreasCadastradas.MARINHA:"Terreno Marginal"===r?P.tiposAreasCadastradas.MARGINAL:P.tiposAreasCadastradas.OUTRO;break;case"acrescido":var r=t.processoDemarcatorio.tipoDemarcacao.noTipoDemarcacao;n="Terreno de Marinha"===r?P.tiposAreasCadastradas.ACRESCIDO_MARINHA:"Terreno Marginal"===r?P.tiposAreasCadastradas.ACRESCIDO_MARGINAL:P.tiposAreasCadastradas.OUTRO;break;case"suspenso":var n=P.tiposAreasCadastradas.SUSPENSO;break;default:var n=P.tiposAreasCadastradas.OUTRO}else var n=P.tipoAreaCadastrada.OUTRO;return n},P.areasCadastradasRegularesAdicionadas=[],P.areasCadastradasAcrescidasAdicionadas=[],P.areasCadastradasSuspensasAdicionadas=[],P.$on("mapmoveend",function(t,e){var n=ol.geom.Polygon.fromExtent(e.extent),r=new ol.format.WKT,o=r.writeGeometry(n,{dataProjection:"EPSG:4326"});a.getAproxAreaFrom4326WktCoordinates(o)<=2e3&&!P.zoomingOut&&P.buscarAreasCadastradas(o,!1)}),P.buscarAreasCadastradas=function(t,e){P.bloquearBuscaAreasCadastradas||(console.log("buscar areas cadastradas..."),P.bloquearBuscaAreasCadastradas=!0,n.buscarAreasCadastradasPorGeometria(t).then(function(n){if(200===n.status){var r=n.data;if(P.areasCadastradas=r,r.processosDemarcatorios.length>0){var o="regular";P.adicionarAreaDeProcessoDemarcatorio(r.processosDemarcatorios,o)}if(r.processosDemarcatoriosAcrescidos.length>0&&(o="acrescido",P.adicionarAreaDeProcessoDemarcatorio(r.processosDemarcatoriosAcrescidos,o)),r.processosDemarcatoriosSuspensos.length>0&&(o="suspenso",P.adicionarAreaDeProcessoDemarcatorio(r.processosDemarcatoriosSuspensos,o)),P.geoidentificacao.dsJustificativaGeometadados?P.geoidentificacao.produtoCartRadio="false":P.geoidentificacao.coProdutosCartograficosRelacionados.length&&(P.geoidentificacao.produtoCartRadio="true",P.buscarProdutos("true",!0)),f(),P.bloquearBuscaAreasCadastradas=!1,P.arquivosAcrescidos&&P.arquivosAcrescidos.content.length&&P.adicionarGeometriaTerrenoAcrescido(P.arquivosAcrescidos.content[0].geomWktAcrescida),e){var i=r.processosDemarcatorios.map(function(t){return t.geomWkt});P.buscarSobreposicoesPorGeometria(t,i)}}}))},P.buscarSobreposicoesPorGeometria=function(t,e){n.buscarSobreposicoesPorGeometria(t,e).then(function(t){var e=new ol.format.WKT,n=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorIntercessoes",n);for(var r=0;r<t.data.length;r++){var o=new ol.Feature({geometry:e.readFeature(t.data[r]).getGeometry()});P.adicionarAreaCadastrada(o,"sobreposta","vectorIntercessoes")}})},P.marcarAreaComoPresente=function(t){P.areasCadastradasAdicionadas.push(t)},P.areaJaPresenteNoMapa=function(t,e){var n=[],r=null;"regular"===e?(n=P.areasCadastradasRegularesAdicionadas,r="idProcessoDemarcatorio"):"acrescido"===e?(r="id",n=P.areasCadastradasAcrescidasAdicionadas):"suspenso"===e&&(r="id",n=P.areasCadastradasSuspensasAdicionadas);for(var o=0;o<n.length;o++)if(n[o][r]===t[r])return!0;return!1},P.marcarAreaComoPresenteNoMapa=function(t,e){var n=[],r=null;"regular"===e?(n=P.areasCadastradasRegularesAdicionadas,r="idProcessoDemarcatorio"):"acrescido"===e?(r="id",n=P.areasCadastradasAcrescidasAdicionadas):"suspenso"===e&&(r="id",n=P.areasCadastradasSuspensasAdicionadas),n.push(t[r])},P.adicionarAreaDeProcessoDemarcatorio=function(t,e){P.inicializarCamadaAreasCadastradas();for(var n="acrescido"===e?"geomWktAcrescida":"geomWkt",r=new ol.format.WKT,o=0;o<t.length;o++){var i=t[o];if(!P.areaJaPresenteNoMapa(i,e)){var a=r.readFeature(i[n]);a.setId(i.idProcessoDemarcatorio),a.setProperties({processo:i,tipoProcesso:e});var s=P.obterTipoAreaCadastrada(i,e);if(P.adicionarAreaCadastrada(a,s,"vectorGeomCadastrados"),!1===i.icSituacaoDemarcacao){var c=a.clone();P.adicionarAreaCadastrada(c,"naoDemarcado","vectorGeomCadastradosRachura"),P.controleLegenda.naoDemarcado=!0}P.marcarAreaComoPresenteNoMapa(i,e)}}},P.inicializarCamadaAreasCadastradas=function(){if(!viewer.getLayer("vectorGeomCadastrados")){var t=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorGeomCadastrados",t)}if(!viewer.getLayer("vectorGeomCadastradosRachura")){var e=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorGeomCadastradosRachura",e)}},P.legendaVisivel=function(){var t=!1;for(var e in P.controleLegenda)"naoDemarcado"!=e&&!0===P.controleLegenda[e]&&(t=!0);return t},P.limparAreasCadastradas=function(){viewer.getLayer("vectorGeomCadastrados")&&(viewer.getLayer("vectorGeomCadastrados").getSource().clear(),P.limparLegendaAreasCadastradas(),P.mapaPossuiAreasCadastradas()||P.limparLegendaAreasCadastradas())},P.limparLegendaAreasCadastradas=function(){P.controleLegenda.marinha=!1,P.controleLegenda.acrescidoMarinha=!1,P.controleLegenda.marginal=!1,P.controleLegenda.acrescidoMarginal=!1,P.controleLegenda.suspenso=!1,P.controleLegenda.naoDemarcado=!1},P.limparLegendaAreasValidadas=function(){P.controleLegenda.novo=!1,P.controleLegenda.invalido=!1,P.controleLegenda.sobreposta=!1,P.controleLegenda.naoDemarcado=!1},P.buscarMunicipios=function(t){return P.isLoading=!0,P.activated=!0,n.municipioSimples.query({geocodigoUf:t},function(t){if(P.isLoading=!1,P.activated=!1,N)for(var e=0;e<t.length;e++){var n=t[e];-1!==P.geoidentificacao.listMunString.indexOf(n.geocodigoMunicipio)&&P.municipiosSelecionados.push(n)}})},P.changeSelectedEtapa=function(){h()},P.zoomUF=function(){var t=P.mviewer.viewer;N||(P.geoidentificacao.listMunString=[],P.municipiosSelecionados=[]);var e=[-73.991437778,-33.750969,-28.840489363,5.27178990699993];if(P.ufsSelecionadas){for(var n=[],r=0;r<P.ufsSelecionadas.length;r++){var o=P.ufsSelecionadas[r];n.push(o.geomUfExtent)}e=a.extentFeaturesExtent(n)}t&&t.start&&t.setZoom(e),S=e},P.limparMunPorUF=function(t){if(document.getElementById(t).getAttribute("aria-selected")){var e=P.municipiosSelecionados;P.municipiosSelecionados&&P.municipiosSelecionados.forEach(function(n){n.geocodigoUf==t&&M(e,"geocodigoUf",t)})}},P.selecionarMunicipio=function(){var t=P.mviewer.viewer,n=[];if(P.geoidentificacao.listMunString=[],P.municipiosSelecionados){for(var r=0;r<P.municipiosSelecionados.length;r++){var o=P.municipiosSelecionados[r];n.push(o.geomMunicipioExntent),P.geoidentificacao.listMunString.push(o.geocodigoMunicipio)}t&&n.length>0&&t.start&&t.setZoom(a.extentFeaturesExtent(n)),e.$broadcast("escolhaMunicipio",{show:!0})}else e.$broadcast("escolhaMunicipio",{show:!1}),t.setZoom(S)},P.mapaPossuiAreasCadastradas=function(){var t=P.areasCadastradasRegularesAdicionadas,e=P.areasCadastradasAcrescidasAdicionadas,n=P.areasCadastradasSuspensasAdicionadas;return t.length>0||e.length>0||n.length>0},P.validarGeometria=function(t){if(P.geometriaValida=!1,map.removeEventListener("click"),A.geometria){viewer.getLayer("vectorCertos")&&viewer.getLayer("vectorCertos").getSource().clear(),viewer.getLayer("vectorCertosRachura")&&viewer.getLayer("vectorCertosRachura").getSource().clear(),viewer.getLayer("vectorIntercessoes")&&viewer.getLayer("vectorIntercessoes").getSource().clear(),viewer.getLayer("vectorErrados")&&viewer.getLayer("vectorErrados").getSource().clear(),viewer.getLayer("vectorForaPais")&&viewer.getLayer("vectorForaPais").getSource().clear(),viewer.getLayer("vectorGeomCadastrados")&&viewer.getLayer("vectorGeomCadastrados").getSource().clear(),viewer.getLayer("vectorProdutosCartograficos")&&viewer.getLayer("vectorProdutosCartograficos").getSource().clear(),viewer.getLayer("vectorIntercessoesProdutosCartograficos")&&viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().clear(),viewer.getLayer("vectorMetadadosIntercessoes")&&viewer.getLayer("vectorMetadadosIntercessoes").getSource().clear(),P.mviewer.viewer.getLayer("vectorGeomCadastradosRachura")&&P.mviewer.viewer.getLayer("vectorGeomCadastradosRachura").getSource().clear();var r={coGeomAreaUniao:P.geoidentificacao.coGeomAreaUniao,geomWkt:A.geometria,listMunString:P.geoidentificacao.listMunString,coEtapaDemarcacao:P.geoidentificacao.coEtapaDemarcacao};n.geoinformacaoValidarGeometria.save(r,function(t){var e=new ol.format.WKT;if(t.certos.length>0){var n=e.readFeature(t.certos.toString()),r=new ol.layer.Vector({source:new ol.source.Vector}),o=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorCertos",r),viewer.addLayer("vectorCertosRachura",o);var i;if(P.geoidentificacao.coTipoDemarcacao===P.tiposDemarcacao.MARINHA?i="marinha":P.geoidentificacao.coTipoDemarcacao===P.tiposDemarcacao.MARGINAL&&(i="marginal"),P.adicionarAreaCadastrada(n,i,"vectorCertos"),!P.geoidentificacao.icSituacaoDemarcacao){var a=n.clone();P.adicionarAreaCadastrada(a,"naoDemarcado","vectorCertosRachura"),P.controleLegenda.naoDemarcado=!0}}if(t.intercessoes.length>0){var s=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorIntercessoes",s);for(var u=0;u<t.intercessoes.length;u++){var n=new ol.Feature({geometry:e.readFeature(t.intercessoes[u].intercessoes).getGeometry()});n.setId(t.intercessoes[u].id);var i="sobreposta";P.adicionarAreaCadastrada(n,i,"vectorIntercessoes")}}if(t.errados.length>0){var d=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorErrados",d);var n=e.readFeature(t.errados.toString()),i="invalido";P.adicionarAreaCadastrada(n,i,"vectorErrados")}if(-1!=t.status.indexOf("2"))l("Atenção!",t.msg.toString(),A.geometria);else if("1"==t.status.toString())P.clearAlert(g),g.textContent(t.msg.toString()).title("Erro!"),c.show(g),P.geometria=null;else{P.clearAlert(g);var p=t.msg.toString();if(t.intercessoes.length>0){var h="Existe uma sobreposição de Geoidentiicação de Área da União nesta Região.";p=p?p+" "+h:h}g.textContent(p).title("Geometria validada com sucesso!"),c.show(g),P.geometriaValida=!0,P.geometria=A.geometria,P.areaAproximadaEmKilometros=A.areaAproximada.toFixed(2),P.showProdCartEmAreaUniao=!0}f()}),map.removeInteraction(draw)
}else e.$broadcast("showLegenda",{show:!1}),P.clearAlert(g),g.textContent("não há geometria"),c.show(g);e.$broadcast("showLegenda",{show:!0})},P.verificarDivergenciaDeArea=function(){Math.abs(Math.floor(P.geoidentificacao.areaAproximada/P.geoidentificacao.areaOficial*100)-100)>10&&(y=c.confirm().title("A Área Oficial informada é muito diferente da área do polígono demarcado. Deseja continuar?").ok("Sim").cancel("Não"),c.show(y).then(function(){},function(){P.geoidentificacao.areaOficial=null}))},P.changeSituacao=function(){P.geoidentificacao.icSituacaoDemarcacao?P.showSuspensao=!1:P.showSuspensao=!0},P.visibilidadeGeomProdCartEditar=function(t){I.setVisible(t)},P.filtroPaginacaoProduto=function(t,e){var n=P.produtosCartograficos.quantidade,r=P.produtosCartograficos.pagina;if(n&&r){var o=0+n*(r-1),i=r*n-1;return e>=o&&e<=i}return!0},P.buscarProdutos=function(t,e){if("true"===t&&A.geometria)P.geoidentificacao.dsJustificativaGeometadados=void 0,P.produtosCartograficos={pagina:1,quantidade:5,order:"idProdutoCartografico",total:0,data:[]},P.buscaProdutosCartograficos=n.produtosCartograficosIntercedidos(A.geometria,{paginacao:{page:null,size:null}}),P.buscaProdutosCartograficos.then(function(t){if(200===t.status){if(viewer.getLayer("vectorProdutosCartograficos"))viewer.getLayer("vectorProdutosCartograficos").getSource().clear();else{var r=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorProdutosCartograficos",r)}if(viewer.getLayer("vectorIntercessoesProdutosCartograficos"))viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().clear();else{var o=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorIntercessoesProdutosCartograficos",o)}var i=t.data,a=i.content;P.tabelaCatalogoMetadados=[];var s=new ol.format.WKT;angular.forEach(a,function(t,e){var r=t.meta,o={id:null,titulo:null,numeroProcesso:null,nomeCadastrador:null,situacao:null,data:null,relacionado:!1};if(o.id=r.idProdutoCartografico,o.titulo=r.noTitulo,null!=r.coSituacaoStatus){var i=r.coSituacaoStatus;angular.forEach(P.situacaoStatus,function(t,e){t.idSituacaoStatus==i&&(o.situacao=t.noSituacaoStatus)})}if(n.geoidentificacaoNumeroProcesso.get({id:Number(r.coGeoidentificacao)},function(t){o.numeroProcesso=t.content}),o.nomeCadastrador=r.coCadastrador,o.data=r.dtCriacaoIdentificacao,P.produtosCartograficos.data.push(o),null!=t.geomProdutoWKT){var a=new ol.Feature({geometry:s.readFeature(t.geomProdutoWKT).getGeometry()});a.setId(r.idProdutoCartografico);var c="produtoCartografico";P.adicionarAreaCadastrada(a,c,"vectorProdutosCartograficos"),a.setStyle(new ol.style.Style({}))}if(null!=t.intersecaoWkt){var a=new ol.Feature({geometry:s.readFeature(t.intersecaoWkt).getGeometry()});a.setId(r.idProdutoCartografico);var c="sobrepostaProdutosCartograficos";P.adicionarAreaCadastrada(a,c,"vectorIntercessoesProdutosCartograficos"),a.setStyle(new ol.style.Style({}))}}),0===P.produtosCartograficos.data.length?u.show({position:"bottom right",template:'<md-toast><div class="md-toast-content">Nenhum registro encontrado</div></md-toast>'}):p(),e&&m()}});else{P.removeProdutosCartograficos(),P.geoidentificacao.coProdutosCartograficosRelacionados=[];for(var r=0;r<P.isProdutosCartograficos.length;r++)P.isProdutosCartograficos[r]=!1}},P.removeProdutosCartograficos=function(){viewer.getLayer("vectorProdutosCartograficos")&&viewer.getLayer("vectorProdutosCartograficos").getSource().clear(),viewer.getLayer("vectorIntercessoesProdutosCartograficos")&&viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().clear(),P.controleLegenda.produtoCartografico=!1,P.controleLegenda.sobrepostaProdutosCartograficos=!1},P.checkSuspensao=function(){return!P.geoidentificacao.icSituacaoDemarcacao},P.submit=function(){if(P.geometria)if(P.geoidentificacaoForm.$valid||!P.geoidentificacaoForm.$error.required&&d(P.geoidentificacaoForm.$error)){P.geoidentificacao.icAtivo=!0,P.geoidentificacao.geomWkt=P.geometria,IS_DRAW=!1,map.removeInteraction(draw),P.geoidentificacao.produtoCartRadio?P.geoidentificacao.dsJustificativaGeometadados=null:A.selected=[],P.geoidentificacao.areaAproximada=Math.ceil(1e6*P.areaAproximadaEmKilometros),P.geoidentificacao.areaOficial=Math.ceil(1e6*P.areaOficialEmKilometros);for(var t=0;t<P.geoidentificacao.portariaComissaoDemarcacao.length;t++)P.geoidentificacao.portariaComissaoDemarcacao[t].anoPortariaComissaoDemarcacao instanceof Date?P.geoidentificacao.portariaComissaoDemarcacao[t].anoPortariaComissaoDemarcacao=Number(P.geoidentificacao.portariaComissaoDemarcacao[t].anoPortariaComissaoDemarcacao.getFullYear()):P.geoidentificacao.portariaComissaoDemarcacao[t].anoPortariaComissaoDemarcacao=P.geoidentificacao.portariaComissaoDemarcacao[t].anoPortariaComissaoDemarcacao-1,P.geoidentificacao.portariaComissaoDemarcacao[t].nuPortariaComissaoDemarcacao&&(P.geoidentificacao.portariaComissaoDemarcacao[t].nuPortariaComissaoDemarcacao=P.geoidentificacao.portariaComissaoDemarcacao[t].nuPortariaComissaoDemarcacao.toString());n.geoinformacaoEditar.save(P.geoidentificacao,function(t){if(0!=A.selected.length){var e={geoIdt:t.idProcessoDemarcatorio,listProd:A.selected};n.processoDemarcatorioProd.save(e,function(e){r.path("/geoidentificacao/"+t.idProcessoDemarcatorio)})}else r.path("/geoidentificacao/"+t.idProcessoDemarcatorio)})}else g.textContent("Necessário informar os campos obrigatórios"),g.title("Erro!"),c.show(g);else g.textContent("Uma geometria precisa ser cadastrada!"),g.title("Erro!"),c.show(g)},P.reset=function(){P.geoidentificacao.nuProcessoDemarcatorio="",P.geoidentificacao.coTipoDemarcacao="",P.geoidentificacao.coEtapaDemarcacao="",P.geoidentificacao.coFinalidadeDemarcacao="",P.geoidentificacao.icSituacaoDemarcacao="",P.geoidentificacao.dsMotivoSuspensa="",P.geoidentificacao.dsTrechoInicio="",P.geoidentificacao.dsTrechoFim="",P.geoidentificacao.dsObservacao="",P.geoidentificacao.arquivoAcrescido=[],P.ufsSelecionadas=[],P.municipiosSelecionados=[],P.listMunString=[],P.areaOficialEmKilometros=null,P.areaAproximadaEmKilometros=null},P.atualizarBotoesDoMapa=function(){P.geoidentificacao.coEtapaDemarcacao&&(P.geoidentificacao.coEtapaDemarcacao<=3?(P.mostrarBotaoDesenhar=!0,P.mostrarBotaoUpload=!0):(P.mostrarBotaoDesenhar=!1,P.mostrarBotaoUpload=!0))},P.paginacaoEditar=function(){var e=P.geometria;P.query.geomWKT=e,P.query.page>0&&P.query.page--,P.query.idProcesso=P.geoidentificacao.idProcessoDemarcatorio,n.geometadadosPorProcesso.save(P.query,function(e){P.query.page,e.length>0&&(P.total=e[0].total);var n=[];P.geometadados=e.content,P.tabelaCatalogoMetadados=[],angular.forEach(P.geometadados,function(e,r){if(t.buscaProdutosCartograficos[r]=e,null!=e.geomRepresentacaoEspacialWKT){var o=new ol.format.WKT,i=o.readFeature(e.geomRepresentacaoEspacialWKT.toString());n.push(i)}});var r=new ol.layer.Vector({source:new ol.source.Vector({features:n}),style:a.estiloRachurado});setTimeout(function(){viewer.addLayer("vectorMetadadosIntercessoes",r)},2e3)}),0===P.total&&u.show({position:"bottom right",template:'<md-toast><div class="md-toast-content">Nenhum registro encontrado</div></md-toast>'})},P.changeTrechoIgualFim=function(){P.trochoIgualFim?(P.geoidentificacao.dsTrechoFim=void 0,P.geoidentificacao.dsTrechoInicio=void 0):P.geoidentificacao.dsTrecho=void 0},P.atualizarMunicipios=function(t){P.geoidentificacao.listMunString=[];for(var e=0;e<t.length;e++){var n=t[e];P.geoidentificacao.listMunString.push(n.geocodigo)}},P.atualizarMunicipioAcrescentados=function(t){P.listMunString=[];for(var e=0;e<t.length;e++){var n=t[e];P.listMunString.push(n.geocodigo)}P.verificarMunicipiosAcrescentados()},P.$on("municipiosDefinidos",function(t,e){P.atualizarMunicipios(e)}),P.$on("municipiosAcrescentadosDefinidos",function(t,e){P.atualizarMunicipioAcrescentados(e)}),P.$on("geometriaDefinida",function(t,e){if("desenho"===e.tipo){P.geoidentificacao.niveisPrecisaoCartografica=P.niveisPrecisaoCartografica[1]}P.geoidentificacao.arquivoArea=e.arquivo,P.geoidentificacao.noArquivoArea=e.noArquivo,w(e.geometria)}),P.$on("geometriaForaDoBrasil",function(t,e){P.mostrarDialogForaDoBrasil(e.callback)}),P.mostrarDialogForaDoBrasil=function(t){y=c.confirm().title("Essa geometria está fora do País. Deseja continuar o cadastro dessa área da União?").ok("Sim").cancel("Não"),c.show(y).then(function(){P.validarGeometria(t)},function(){A.geometria=null,P.geometria=null,map.removeInteraction("draw")})},P.atualizarSituacao=function(t){1===t||2===t||3===t?(P.geoidentificacao.icSituacaoDemarcacao=!1,P.geoidentificacao.dsSituacaoDemarcacao="Não Demarcado"):(P.geoidentificacao.icSituacaoDemarcacao=!0,P.geoidentificacao.dsSituacaoDemarcacao="Demarcado")},n.geoinformacao.get({id:s.id},function(t){P.geoidentificacao=t,P.oldPortariaComissaoDemarcacao=P.geoidentificacao.portariaComissaoDemarcacao,P.getArquivosAcrescidos(),P.atualizarSituacao(P.geoidentificacao.coEtapaDemarcacao),P.areaOficialEmKilometros=P.geoidentificacao.areaOficial/1e6,P.areaAproximadaEmKilometros=P.geoidentificacao.areaAproximada/1e6,P.geoidentificacao.icSituacaoDemarcacao?P.showSuspensao=!1:(P.geoidentificacao.dsSuspensao=t.dsMotivoSuspensa,P.showSuspensao=!0),P.geoidentificacao.portariaComissaoDemarcacao&&P.geoidentificacao.portariaComissaoDemarcacao.length?angular.forEach(P.geoidentificacao.portariaComissaoDemarcacao,function(t){t.anoPortariaComissaoDemarcacao+=1,t.nuPortariaComissaoDemarcacao=Number(t.nuPortariaComissaoDemarcacao)}):(P.geoidentificacao.portariaComissaoDemarcacao=[],P.addPortaria()),P.getSuspensoes(P.geoidentificacao.idProcessoDemarcatorio),A.geometria=t.geomWkt,P.geometria=A.geometria.toString(),P.geoidentificacao.listMunString=[];for(var r=new Set,o=0;o<t.municipio.length;o++){var i=t.municipio[o];r.add(i.geocodigoUf),P.geoidentificacao.listMunString.push(i.geocodigoMunicipio)}r=Array.from(r),n.uf.query(function(t){P.ufs=t;for(var e=0;e<P.ufs.length;e++){var n=P.ufs[e];-1!==r.indexOf(n.geocodigoUf)&&P.ufsSelecionadas.push(n)}}),P.geoidentificacao.dsTrecho&&(P.trochoIgualFim=!0),P.changeSelectedEtapa(),setTimeout(function(){P.buscarAreasCadastradas(t.geomWkt,!0),e.$broadcast("geometriaEditada")},7e3),P.showProdCartEmAreaUniao=!0})}var g=c.alert().clickOutsideToClose(!1).textContent().ok("OK");P.uploadFile=function(t){if(t)return"POLYGON"===A.geometria.substring(0,7)?void P.uploadErro("Favor salvar a geometria da Área da União antes de acrescentar uma área"):void o.uploadShapefile(t).then(function(t){if(200===t.status)if(t.data.data&&400===t.data.data.status)P.uploadErro(t.data.data.msg);else if(t.data&&200===t.data.status){var n=t.data.data.data;o.transformarWkt(n,4674,4326).then(function(n){if(200===n.status){var r=n.data.geometriaWkt,o=parseFloat(a.getAproxAreaFrom4326WktCoordinates(r)).toFixed(2);P.validarPontoAdjacente(r)?(P.terrenoAcrescido={areaAproximada:o,areaOficial:void 0,arquivo:t.data.data.byteArray,nomeArquivo:t.data.data.nome,isRemovido:!1,geomWktAcrescida:r},e.$broadcast("geometriaAcrescentada",r)):P.uploadErro("Favor uncluir um polígono adjacente.")}else console.error(t.data)})}else P.terrenoAcrescido=void 0,P.uploadErro("Erro ao enviar arquivo")}).catch(function(t){P.terrenoAcrescido=void 0,P.uploadErro("Erro ao enviar arquivo")})},P.adicionarGeometriaTerrenoAcrescido=function(t){var e=P.mviewer.viewer,n=new ol.format.WKT,r=new ol.layer.Vector({source:new ol.source.Vector}),o=n.readFeature(t),i=P.geoidentificacao.coTipoDemarcacao===P.tiposDemarcacao.MARINHA?"#aad08e61":"#f8cbad5c",a=P.geoidentificacao.coTipoDemarcacao===P.tiposDemarcacao.MARINHA?"#AAD08E":"#F8CBAD";o.setStyle(new ol.style.Style({fill:new ol.style.Fill({color:i}),stroke:new ol.style.Stroke({color:a})})),r.getSource().addFeature(o),e.addLayer("camadaGeometriaAcrescida",r),e.setZoom(o.getGeometry().getExtent()),P.geoidentificacao.coTipoDemarcacao===P.tiposDemarcacao.MARGINAL?P.controleLegenda.acrescidoMarginal=!0:P.controleLegenda.acrescidoMarinha=!0},P.verificarMunicipiosAcrescentados=function(){if(!P.listMunString||!P.listMunString.length||P.listMunString.length!==P.geoidentificacao.listMunString.length)return P.terrenoAcrescido=void 0,void P.uploadErro("A geometria informada faz sobreposição com municípios diferentes da área do Terreno "+(P.geoidentificacao.coTipoDemarcacao===P.tiposDemarcacao.MARINHA?"de Marinha":"Marginal. Favor incluir um polígono que esteja nos mesmos municípios da geoidentificação que está sendo editada."));angular.forEach(P.listMunString,function(t){if(!P.geoidentificacao.listMunString.find(function(e){return e===t}))return P.terrenoAcrescido=void 0,void P.uploadErro("A geometria informada faz sobreposição com municípios diferentes da área do Terreno "+(P.geoidentificacao.coTipoDemarcacao===P.tiposDemarcacao.MARINHA?"de Marinha":"Marginal. Favor incluir um polígono que esteja nos mesmos municípios da geoidentificação que está sendo editada."))})},P.validarPontoAdjacente=function(t){var e,n="POLYGON"===A.geometria.substring(0,7),r=a.getCoordinatesFromGeometry(A.geometria),o=a.getCoordinatesFromGeometry(t);e=n?turf.polygon([r[0]]):turf.polygon([r[0][0]]);var i=turf.polygon([o[0]]);return turf.intersect(e,i)},P.uploadErro=function(t){P.clearAlert(g),g.title(t),c.show(g)},P.$on("uploadfalhou",function(t,e){P.clearAlert(g),g.title(e),c.show(g)}),P.$on("mensagemErro",function(t,e){P.clearAlert(g),g.title(e),c.show(g)}),P.clearAlert=function(t){t.textContent(""),t.title("")}}),P.query={numProcesso:null,geomWKT:null,size:4,page:1},P.addPortaria=function(){P.geoidentificacao.portariaComissaoDemarcacao.push({id:P.geoidentificacao.portariaComissaoDemarcacao.length+1}),P.isPortariaAlterada=Boolean(P.tipoEtapa())},P.removePortaria=function(t){P.geoidentificacao.portariaComissaoDemarcacao.splice(P.geoidentificacao.portariaComissaoDemarcacao.indexOf(t),1)},P.vericarPortariaAlterada=function(t){if(P.oldPortariaComissaoDemarcacao.length)if(t&&t.id_portaria_comissao_demarcacao){var e=P.oldPortariaComissaoDemarcacao.find(function(e){return e.id_portaria_comissao_demarcacao===t.id_portaria_comissao_demarcacao});e.nu_portaria_comissao_demarcacao===t.nu_portaria_comissao_demarcacao&&e.ano_portaria_comissao_demarcacao===t.ano_portaria_comissao_demarcacao||(P.isPortariaAlterada=Boolean(P.tipoEtapa()))}else P.isPortariaAlterada=Boolean(P.tipoEtapa());else P.isPortariaAlterada=Boolean(P.tipoEtapa())},P.tipoEtapa=function(){return!(!P.geoidentificacao.coEtapaDemarcacao||1===P.geoidentificacao.coEtapaDemarcacao)},P.getSuspensoes=function(t){P.paginacaoSuspensao={idProcessoDemarcatorio:t,isRemovido:!1,page:0,size:10},n.pageSuspensao.get(P.paginacaoSuspensao,function(t){P.geoidentificacaoSuspensaoPage=t,P.paginacaoSuspensao.page++,t.content.length&&(P.controleLegenda.suspenso=!0)})},P.filtrarSuspensao=function(){P.paginacaoSuspensao.page--,n.pageSuspensao.get(P.paginacaoSuspensao,function(t){P.geoidentificacaoSuspensaoPage=t,P.paginacaoSuspensao.page++})},P.adicionarSuspensao=function(e,n,r){if("POLYGON"===A.geometria.substring(0,7))return void P.uploadErro("Favor salvar a geometria da Área da União antes de incluir uma área suspensa");P.geoidentificacao&&c.show({controller:"GeoidentificacaoSuspenderController",templateUrl:"scripts-geoidentificacao/geoidentificacao/geoidentificacao_suspender.html",locals:{geoidentificacao:P.geoidentificacao,geometria:P.geometria,idGeoidentificacaoSuspensao:n,isVisualizar:r},parent:angular.element(document.body),targetEvent:e,fullscreen:t.customFullscreen}).then(function(t){t&&P.getSuspensoes(P.geoidentificacao.idProcessoDemarcatorio)})},P.removerSuspensao=function(t,e){t&&(confirm=c.confirm().title("Tem certeza que deseja remover esta suspensão?").ok("Sim").cancel("Não"),c.show(confirm).then(function(){n.removerSuspensao.delete({id:t},function(){P.geoidentificacaoSuspensaoPage.content.splice(e,1),P.geoidentificacao.existeSuspensaoRemovida=!0,C()})}))},P.toggleVerSuspensoesRemovidas=function(){P.isSuspensoesRemovidas=!P.isSuspensoesRemovidas,P.isSuspensoesRemovidas&&P.getSuspensoesRemovidas(P.geoidentificacao.idProcessoDemarcatorio)},P.getSuspensoesRemovidas=function(t){P.paginacaoSuspensaoRemovida={idProcessoDemarcatorio:t,isRemovido:!0,page:0,size:10},n.pageSuspensao.get(P.paginacaoSuspensaoRemovida,function(t){P.geoidentificacaoSuspensaoRemovidaPage=t,P.paginacaoSuspensaoRemovida.page++})},P.filtrarSuspensaoRemovida=function(){P.paginacaoSuspensaoRemovida.page--,n.pageSuspensao.get(P.paginacaoSuspensaoRemovida,function(t){P.geoidentificacaoSuspensaoRemovidaPage=t,P.paginacaoSuspensaoRemovida.page++})},P.getArquivosAcrescidos=function(){P.paginacaoArquivoAcrescido={idProcessoDemarcatorio:P.geoidentificacao.idProcessoDemarcatorio,isRemovido:!1,page:0,size:10},n.pageArquivoAcrescido.get(P.paginacaoArquivoAcrescido,function(t){P.arquivosAcrescidos=t,P.paginacaoArquivoAcrescido.page++})},P.filtrarArquivoAcrescido=function(){P.paginacaoArquivoAcrescido.page--,n.pageArquivoAcrescido.get(P.paginacaoArquivoAcrescido,function(t){P.arquivosAcrescidos=t,P.paginacaoArquivoAcrescido.page++})},P.toggleVerTerrenosRemovidos=function(){P.verTerrenosRemovidos=!P.verTerrenosRemovidos,P.verTerrenosRemovidos&&P.getArquivosAcrescidosRemovidos()},P.getArquivosAcrescidosRemovidos=function(){P.paginacaoArquivoAcrescidoRemovidos={idProcessoDemarcatorio:P.geoidentificacao.idProcessoDemarcatorio,isRemovido:!0,page:0,size:10},n.pageArquivoAcrescido.get(P.paginacaoArquivoAcrescidoRemovidos,function(t){P.arquivosRemovido=t,P.paginacaoArquivoAcrescidoRemovidos.page++})},P.filtrarArquivoAcrescidoRemovido=function(){P.paginacaoArquivoAcrescidoRemovidos.page--,n.pageArquivoAcrescido.get(P.paginacaoArquivoAcrescidoRemovidos,function(t){P.arquivosRemovido=t,P.paginacaoArquivoAcrescidoRemovidos.page++})},P.editarTerrenoAcrescido=function(t,e){P.terrenoAcrescido=t,P.arquivosAcrescidos.content.splice(e,1)},P.excluirTerrenoAcrescido=function(t,e){confirm=c.confirm().title("Tem certeza que deseja remover este terreno acrescido?").ok("Sim").cancel("Não"),c.show(confirm).then(function(){n.excluirArquivoAcrescido.delete({id:t},function(){P.arquivosAcrescidos.content.splice(e,1),P.geoidentificacao.existeTerrenoAcrescidoRemovido=!0,P.mviewer.viewer.getLayer("camadaGeometriaAcrescida")&&P.mviewer.viewer.getLayer("camadaGeometriaAcrescida").getSource().clear(),C()})})},P.adicionarTerrenoAcrescido=function(){P.terrenoAcrescido&&P.terrenoAcrescido.areaOficial?(P.terrenoAcrescido.idProcessoDemarcatorio=P.geoidentificacao.idProcessoDemarcatorio,n.gravarArquivoAcrescido.save(P.terrenoAcrescido,function(t){P.terrenoAcrescido=void 0,P.getArquivosAcrescidos(),P.adicionarGeometriaTerrenoAcrescido(t.geomWktAcrescida)})):e.$broadcast("mensagemErro","Necessário preencher os campos obrigatórios.")},P.verificarDivergenciaDeAreaAcrescida=function(){if(P.terrenoAcrescido&&P.terrenoAcrescido.arquivo){Math.abs(Math.floor(P.terrenoAcrescido.areaAproximada/P.terrenoAcrescido.areaOficial*100)-100)>10&&(confirm=c.confirm().title("A Área Oficial informada é muito diferente da área do polígono demarcado. Deseja continuar?").ok("Sim").cancel("Não"),c.show(confirm).then(function(){},function(){P.terrenoAcrescido.areaOficial=null}))}},P.download=function(t){if(t){var e=window.document.createElement("a");e.href="data:application/zip;base64,"+t.arquivo,e.download=t.nomeArquivo,document.body.appendChild(e),e.click(),document.body.removeChild(e)}},P.verAreaSuspensa=function(t){P.legendaSuspenso=!0;var e=P.mviewer.viewer,n=new ol.format.WKT,r=new ol.layer.Vector({source:new ol.source.Vector}),o=n.readFeature(t.geomWkt);o.setStyle(new ol.style.Style({stroke:new ol.style.Stroke({color:"#FF0000"})})),o.setId(t.id),r.getSource().addFeature(o),e.addLayer("camadaGeometriaSuspensa_"+t.id,r),e.setZoom(o.getGeometry().getExtent()),window.scrollTo(0,0),h(t,o),P.controleLegenda.suspenso=!0},P.consultaDocumentoSei=function(){if(P.geoidentificacao.nuProcessoDemarcatorio)if(P.geoidentificacao.nuProcessoDemarcatorio)n.consultaProcessoSei.save({numeroProcesso:P.geoidentificacao.nuProcessoDemarcatorio},function(t){if(null===t.idProcedimento){var e=c.alert().clickOutsideToClose(!1).textContent("Nº do processo nã encontrado.").ok("OK");c.show(e)}else P.processoSEI=t},function(t){var e=c.alert().clickOutsideToClose(!1).textContent("Nº do processo nã encontrado.").ok("OK");c.show(e)});else{var t=c.alert().clickOutsideToClose(!1).textContent("Necessário informar um documento.").ok("OK");c.show(t)}},P.selecionarProdutoCartografico=function(t,e){if(e)P.geoidentificacao.coProdutosCartograficosRelacionados.push(t),y(t),_(t);else{var n=P.geoidentificacao.coProdutosCartograficosRelacionados.findIndex(function(e){return e===t});n>=0&&(P.geoidentificacao.coProdutosCartograficosRelacionados.splice(n,1),v(t),b(t))}},P.cancelar=function(){r.path("/geoidentificacao"),setTimeout(function(){d.location.reload()},1e3)}}t.$inject=["$scope","$rootScope","giService","$location","mapService","giUsuario","giOlFunctions","$stateParams","$mdDialog","$mdToast","$filter","$window"],angular.module("su-geoidentificacao").component("geoidentificacaoeditar",{templateUrl:"scripts-geoidentificacao/geoidentificacao/geoidentificacao_editar.html",controller:t})}(),function(){"use strict";function t(t,e,n,r,o,i,a,s,c,u,l,d,p){var f,h=t,g=e;document.getElementById("info-closer");h.maxDate=new Date;var m=i.alert().clickOutsideToClose(!1).textContent().ok("OK"),y=i.confirm().title("Essa Geoidentificação de Área da União possui geometria cadastrada. Ao excluí-lo, sua geometria será perdida.").ok("Sim").cancel("Cancelar"),v=function(t){n.show({position:"bottom right",template:'<md-toast><div class="md-toast-content">'+t+"</div></md-toast>"})};s.usuarioLogado(),h.mviewer={},h.areasCadastradasRegularesAdicionadas=[],h.areasCadastradasAcrescidasAdicionadas=[],h.areasCadastradasSuspensasAdicionadas=[],h.mostrarLegenda=!1,h.geoidentificacao=void 0,h.tiposDemarcacao={MARINHA:1,MARGINAL:2},h.estiloAreasCadastradasPorTipo={marinha:{cor:"#548235",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(84, 130, 53, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(84, 130, 53, 1)"})})},acrescidoMarinha:{cor:"#AAD08E",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(170, 208, 142, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(170, 208, 142, 1)"})})},marginal:{cor:"#FFD966",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(255, 217, 102, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(255, 217, 102, 1)"})})},acrescidoMarginal:{cor:"#F8CBAD",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(248, 203, 173, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(248, 203, 173, 1)"})})},suspenso:{cor:"#FF0000",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(0, 0, 0,)"}),stroke:new ol.style.Stroke({color:"rgba(255, 0, 0, 1)"})})},sobreposta:{cor:"#6600CC",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(102, 0, 204, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(102, 0, 204, 1)"})})},naoDemarcado:{estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:u.getHashedPattern()}),stroke:new ol.style.Stroke({color:"rgba(0, 0, 0, 1)"})})},invalido:{estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(255, 0, 0, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(255, 0, 0, 1)"})})},outro:{cor:"#666666",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(102, 102, 102, 0.6)"}),stroke:new ol.style.Stroke({color:"rgba(102, 102, 102, 1)"})})}},h.controleLegenda={marinha:!1,acrescidoMarinha:!1,marginal:!1,acrescidoMarginal:!1,suspenso:!1,sobreposta:!1,outro:!1,invalido:!1,produtoCartografico:!1,sobrepostaProdutosCartograficos:!1,naoDemarcado:!1},h.tiposAreasCadastradas={MARINHA:"marinha",ACRESCIDO_MARINHA:"acrescidoMarinha",MARGINAL:"marginal",ACRESCIDO_MARGINAL:"acrescidoMarginal",SUSPENSO:"suspenso",SOPREPOSTA:"sobreposta",OUTRO:"outro",NOVO:"novo",INVALIDO:"invalido",PRODUTOS_CARTOGRAFICO:"produtoCartografico",SOBREPOSTA_PRODUTOS_CARTOGRAFICOS:"sobrepostaProdutosCartograficos"},s.usuarioLogado(function(){function n(){console.log("Adicionando listener de popup...");var t=h.mviewer.viewer.map;t.getViewport().oncontextmenu=null,t.getViewport().oncontextmenu=function(n){var o=[];o.push(n.layerX),o.push(n.layerY);var i=t.getCoordinateFromPixel(o),a=t.forEachFeatureAtPixel(o,function(t){return!(!t||void 0==t.getId()||!t.get("processType")||"regular"!==t.get("processType"))&&t});return a&&r.geoinformacao.get({id:a.getId(),popup:!1},function(t){e.$broadcast("popupConteudo",{responsePopup:t}),f.setPosition(i)}),!1}}function o(){var t=["bottom","center","top"],e=t[Math.floor(Math.random()*t.length)],n=["left","center","right"];return e+"-"+n[Math.floor(Math.random()*n.length)]}h.authenticatedCadastrar=-1!==g.usuario.permissoes.indexOf(s.cadastrar),h.authenticatedEditar=-1!==g.usuario.permissoes.indexOf(s.editar),h.authenticatedExcluir=-1!==g.usuario.permissoes.indexOf(s.excluir),h.alertPermissao=function(t){s.alertPermissao(t)},h.geoidentificacoes=[],h.geoserverUrl=a.geoserverUrl,g.geoIdentificacaoQuery?h.query=g.geoIdentificacaoQuery:h.query={geocodigoUf:void 0,size:5,page:1},h.cadastradores=r.cadastradorSelect.query(),r.regiao.query(function(t){h.regioes=t}),r.uf.query(function(t){h.ufs=t}),r.etapaGeoidentificacao.get(function(t){h.listEtapaGeoidentificacao=t.data}),r.finalidadeGeoidentificacao.get(function(t){h.listFinalidadeGeoidentificacao=t.data}),h.buscarMunicipios=function(){h.query.geocodigoMunicipio=null,h.municipios=[],h.query.geocodigoUf?(h.isLoadingConsMun=!0,h.activatedConsMun=!0,r.municipioSimples.query({geocodigoUf:h.query.geocodigoUf},function(t){h.municipios=t,h.isLoadingConsMun=!1,h.activatedConsMun=!1})):h.zoom(h.extent)},h.formataDtIni=function(){c.formataDataRegra(document.querySelector("#dtCadastroIni .md-datepicker-input"))},h.formataDtFim=function(){c.formataDataRegra(document.querySelector("#dtCadastroFim .md-datepicker-input"))},h.extentNacional=[-73.991437778,-33.750969,-28.840489363,5.27178990699993],h.extent=h.extentNacional,h.extentUf=null,h.zoom=function(t){h.mviewer.viewer.setZoom(t)},h.zoomRegiao=function(t){h.extent=t,h.zoom(t)},h.zoomUf=function(t){h.extentUf=t,h.zoom(t)},h.limparLegendaAreasCadastradas=function(){h.controleLegenda.marinha=!1,h.controleLegenda.acrescidoMarinha=!1,h.controleLegenda.marginal=!1,h.controleLegenda.acrescidoMarginal=!1,h.controleLegenda.suspenso=!1,h.controleLegenda.naoDemarcado=!1},h.limparLegendaAreasValidadas=function(){h.controleLegenda.novo=!1,h.controleLegenda.invalido=!1,h.controleLegenda.sobreposta=!1,h.controleLegenda.naoDemarcado=!1},h.limparLayers=function(){h.limparLegendaAreasCadastradas(),h.limparLegendaAreasValidadas(),h.mviewer.viewer.getLayer("vectorCertos")&&h.mviewer.viewer.getLayer("vectorCertos").getSource().clear(),h.mviewer.viewer.getLayer("vectorCertosRachura")&&h.mviewer.viewer.getLayer("vectorCertosRachura").getSource().clear(),h.mviewer.viewer.getLayer("vectorIntercessoes")&&h.mviewer.viewer.getLayer("vectorIntercessoes").getSource().clear(),h.mviewer.viewer.getLayer("vectorErrados")&&h.mviewer.viewer.getLayer("vectorErrados").getSource().clear(),h.mviewer.viewer.getLayer("vectorForaPais")&&h.mviewer.viewer.getLayer("vectorForaPais").getSource().clear(),h.mviewer.viewer.getLayer("vectorGeomCadastrados")&&h.mviewer.viewer.getLayer("vectorGeomCadastrados").getSource().clear(),h.mviewer.viewer.getLayer("vectorProdutosCartograficos")&&h.mviewer.viewer.getLayer("vectorProdutosCartograficos").getSource().clear(),h.mviewer.viewer.getLayer("vectorIntercessoesProdutosCartograficos")&&h.mviewer.viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().clear(),h.mviewer.viewer.getLayer("vectorMetadadosIntercessoes")&&h.mviewer.viewer.getLayer("vectorMetadadosIntercessoes").getSource().clear(),h.mviewer.viewer.getLayer("vectorGeomCadastradosRachura")&&h.mviewer.viewer.getLayer("vectorGeomCadastradosRachura").getSource().clear(),h.mviewer.viewer.getLayer("vectorProdutosCartograficos")&&h.mviewer.viewer.getLayer("vectorProdutosCartograficos").getSource().clear(),h.mviewer.viewer.getLayer("vectorIntercessoesProdutosCartograficos")&&h.mviewer.viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().clear()},h.filtrar=function(){h.limparLayers(),h.query.page>0&&h.query.page--,c.showLoading(),r.geoidentificacao.get(h.query,function(t){c.hideLoading(),h.query.page++,h.catalogoMetadados=t,h.total=t.totalElements,h.geoidentificacoes=t.content,g.geoIdentificacaoQuery=h.query,0===h.total&&v("Nenhum registro encontrado")})},h.excluir=function(t){i.show(y).then(function(){r.excluirGeoidentificacao.get({id:t},function(t){!0===t.$resolved&&(v("Geoidentificação excluido com sucesso"),h.filtrar())},function(){m.textContent("Não possivel excluir a Geoidentificação!"),m.title("Erro!"),i.show(m)})},function(){i.hide()})},h.limparForm=function(){h.query.nuProcessoDemarcatorio=void 0,h.query.coTipoDemarcacao=void 0,h.query.coEtapaDemarcacao=void 0,h.query.coFinalidadeDemarcacao=void 0,h.query.icSituacaoDemarcacao=void 0,h.query.geocodigoUf=void 0,h.query.geocodigoMunicipio=void 0,h.municipios=null,h.query.dtInicial=void 0,h.query.dtFinal=void 0},h.filtrar(),h.query.geocodigoUf&&h.buscarMunicipios(),h.buscarRegiao=function(){h.isLoadingConsUf=!0,h.activatedConsUf=!0,h.query.geocodigoUf=null,h.query.geocodigoMunicipio=null,h.ufs=[],h.municipios=null,r.ufPorRegiao.query({idRegiao:h.query.regiaoUF},function(t){h.ufs=t,h.isLoadingConsUf=!1,h.activatedConsUf=!1}),h.query.regiaoUF||h.zoomRegiao(h.extentNacional)},h.$on("buscarLocalidade",function(t,e){h.limparLayers(),h.buscarAreasCadastradas(e.geometria),n()}),h.obterTipoAreaCadastrada=function(t,e){var n=null;if(e)switch(e){case"regular":var r=t.tipoDemarcacao.noTipoDemarcacao;n="Terreno de Marinha"===r?h.tiposAreasCadastradas.MARINHA:"Terreno Marginal"===r?h.tiposAreasCadastradas.MARGINAL:h.tiposAreasCadastradas.OUTRO;break;case"acrescido":var r=t.processoDemarcatorio.tipoDemarcacao.noTipoDemarcacao;n="Terreno de Marinha"===r?h.tiposAreasCadastradas.ACRESCIDO_MARINHA:"Terreno Marginal"===r?h.tiposAreasCadastradas.ACRESCIDO_MARGINAL:h.tiposAreasCadastradas.OUTRO;break;case"suspenso":var n=h.tiposAreasCadastradas.SUSPENSO;break;default:var n=h.tiposAreasCadastradas.OUTRO}else var n=h.tipoAreaCadastrada.OUTRO;return n},h.buscarAreasCadastradas=function(t){r.buscarAreasCadastradasPorGeometria(t).then(function(e){if(200===e.status){var n=e.data;if(h.areasCadastradas=n,n.processosDemarcatorios.length>0){var r="regular";h.adicionarAreaDeProcessoDemarcatorio(n.processosDemarcatorios,r)}n.processosDemarcatoriosAcrescidos.length>0&&(r="acrescido",h.adicionarAreaDeProcessoDemarcatorio(n.processosDemarcatoriosAcrescidos,r)),n.processosDemarcatoriosSuspensos.length>0&&(r="suspenso",h.adicionarAreaDeProcessoDemarcatorio(n.processosDemarcatoriosSuspensos,r)),h.buscarProdutos(t)}})},h.buscarSobreposicoesPorGeometria=function(t,e){r.buscarSobreposicoesPorGeometria(t,e).then(function(t){var e=new ol.format.WKT,n=new ol.layer.Vector({source:new ol.source.Vector});h.mviewer.viewer.addLayer("vectorIntercessoes",n);for(var r=0;r<t.data.length;r++){var o=new ol.Feature({geometry:e.readFeature(t.data[r]).getGeometry()});h.adicionarAreaCadastrada(o,"sobreposta","vectorIntercessoes")}})},h.marcarAreaComoPresente=function(t){h.areasCadastradasAdicionadas.push(t)},h.areaJaPresenteNoMapa=function(t,e){var n=[],r=null;"regular"===e?(n=h.areasCadastradasRegularesAdicionadas,r="idProcessoDemarcatorio"):"acrescido"===e?(r="id",n=h.areasCadastradasAcrescidasAdicionadas):"suspenso"===e&&(r="id",n=h.areasCadastradasSuspensasAdicionadas);for(var o=0;o<n.length;o++)if(n[o][r]===t[r])return!0;return!1},h.marcarAreaComoPresenteNoMapa=function(t,e){var n=[],r=null;"regular"===e?(n=h.areasCadastradasRegularesAdicionadas,r="idProcessoDemarcatorio"):"acrescido"===e?(r="id",n=h.areasCadastradasAcrescidasAdicionadas):"suspenso"===e&&(r="id",n=h.areasCadastradasSuspensasAdicionadas),n.push(t[r])},h.adicionarAreaDeProcessoDemarcatorio=function(t,e){h.inicializarCamadaAreasCadastradas()
;for(var n="acrescido"===e?"geomWktAcrescida":"geomWkt",r=new ol.format.WKT,o=0;o<t.length;o++){var i=t[o];if(!h.areaJaPresenteNoMapa(i,e)){var a=r.readFeature(i[n]);a.setId(i.idProcessoDemarcatorio),a.set("processType",e);var s=h.obterTipoAreaCadastrada(i,e);if(h.adicionarAreaCadastrada(a,s,"vectorGeomCadastrados"),!1===i.icSituacaoDemarcacao){var c=a.clone();h.adicionarAreaCadastrada(c,"naoDemarcado","vectorGeomCadastradosRachura"),h.controleLegenda.naoDemarcado=!0}h.marcarAreaComoPresenteNoMapa(i,e)}}},t.$on("overlayCriado",function(t,e){f=e.overlayPopUP}),h.adicionarAreaCadastrada=function(t,e,n){h.controleLegenda[e]=!0,"produtoCartografico"===e?e="outro":"sobrepostaProdutosCartograficos"===e&&(e="sobreposta");var r=h.estiloAreasCadastradasPorTipo[e].estiloFeature;t.setStyle(r),h.mviewer.viewer.getLayer(n).getSource().addFeature(t)},h.inicializarCamadaAreasCadastradas=function(){if(!h.mviewer.viewer.getLayer("vectorGeomCadastrados")){var t=new ol.layer.Vector({source:new ol.source.Vector});h.mviewer.viewer.addLayer("vectorGeomCadastrados",t)}if(!h.mviewer.viewer.getLayer("vectorGeomCadastradosRachura")){var e=new ol.layer.Vector({source:new ol.source.Vector});h.mviewer.viewer.addLayer("vectorGeomCadastradosRachura",e)}},h.legendaVisivel=function(){var t=!1;for(var e in h.controleLegenda)"naoDemarcado"!=e&&!0===h.controleLegenda[e]&&(t=!0);return t},h.limparAreasCadastradas=function(){h.mviewer.viewer.getLayer("vectorGeomCadastrados")&&(h.mviewer.viewer.getLayer("vectorGeomCadastrados").getSource().clear(),h.limparLegendaAreasCadastradas(),h.mapaPossuiAreasCadastradas()||h.limparLegendaAreasCadastradas())},h.buscarProdutos=function(t){h.buscaProdutosCartograficos=r.produtosCartograficosIntercedidos(t,{paginacao:{page:null,size:100}}),h.buscaProdutosCartograficos.then(function(t){if(200===t.status){if(h.mviewer.viewer.getLayer("vectorProdutosCartograficos"))h.mviewer.viewer.getLayer("vectorProdutosCartograficos").getSource().clear();else{var e=new ol.layer.Vector({source:new ol.source.Vector});h.mviewer.viewer.addLayer("vectorProdutosCartograficos",e)}if(h.mviewer.viewer.getLayer("vectorIntercessoesProdutosCartograficos"))h.mviewer.viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().clear();else{var n=new ol.layer.Vector({source:new ol.source.Vector});h.mviewer.viewer.addLayer("vectorIntercessoesProdutosCartograficos",n)}var r=t.data,o=r.content,i=new ol.format.WKT;angular.forEach(o,function(t,e){var n=t.meta;if(null!=t.geomProdutoWKT){var r=new ol.Feature({geometry:i.readFeature(t.geomProdutoWKT).getGeometry()});n&&r.setId(n.idProdutoCartografico);var o="produtoCartografico";h.adicionarAreaCadastrada(r,o,"vectorProdutosCartograficos"),r.setStyle(new ol.style.Style({}))}if(null!=t.intersecaoWkt){var r=new ol.Feature({geometry:i.readFeature(t.intersecaoWkt).getGeometry()});n&&r.setId(n.idProdutoCartografico);var o="sobrepostaProdutosCartograficos";h.adicionarAreaCadastrada(r,o,"vectorIntercessoesProdutosCartograficos"),r.setStyle(new ol.style.Style({}))}})}})},h.verNoMapa=function(t){t&&t.geomWkt&&(h.limparLayers(),h.buscarAreasCadastradas(t.geomWkt),h.showInfoArea(t,void 0))},h.showInfoArea=function(t,e){h.geoidentificacao=t;var n=document.getElementById("info-popup"),r=document.getElementById("info-popup"),i=new ol.format.WKT,a=i.readFeature(t.geomWkt);window.scrollTo(0,0),h.mviewer.viewer.setZoom(a.getGeometry().getExtent());var s="";angular.forEach(t.portariaComissaoDemarcacao,function(t,e){s+=t.nuPortariaComissaoDemarcacao+"/"+t.anoPortariaComissaoDemarcacao+" "});var c=t.dsTrecho?t.dsTrecho:"Inicial: "+t.dsTrechoInicio+" Final: "+t.dsTrechoFim;r.innerHTML='<div style="float: right"><a href="#" id="info-closer" class="ol-popup-closer"></a></div><div><b>Tipo<b>:&nbsp;'+t.tipoDemarcacao.noTipoDemarcacao+"</div><div><b>Nº processo<b/>:&nbsp;"+t.nuProcessoDemarcatorio+"</div><div><b>Trecho<b/>:&nbsp;"+c+"</div><div><b>Portaria de Comissão de Demarcação<b/>:&nbsp;"+s+"</div><div><b>Etapa<b/>:&nbsp;"+t.etapaDemarcacao.noEtapaDemarcacao+"</div><div><b>Situação<b/>:&nbsp;"+(t.icSituacaoDemarcacao?"Demarcado":"Não Demarcado")+"</div><div><b>Demanda Geradora<b/>:&nbsp;"+t.finalidadeDemarcacao.noFinalidadeDemarcacao+"</div><div><b>UF(s)<b/>:&nbsp;"+t.nomeUF+"</div><div><b>Município(s)<b/>:&nbsp;"+t.nomeMunicipios+"</div><div><b>Área Aproximada<b/>:&nbsp;"+t.areaAproximada+"</div><div><b>Nível de Precisão<b/>:&nbsp;"+(t.niveisPrecisaoCartografica?t.niveisPrecisaoCartografica.dsNiveisPrecisaoCartografica:"")+"</div><div><b>Cadastrador<b/>:&nbsp;"+t.noCadastrador+"</div><div><b>Data de Cadastro<b/>:&nbsp;"+l("date")(t.dtCadastro,"dd/MM/yyyy")+"</div><div><b>Observação<b/>:&nbsp;"+t.dsObservacao+"</div>";var u=new ol.Overlay({element:n});if(h.mviewer.viewer.map.addOverlay(u),n.style.display="block",e)u.setPosition(e);else{var d=turf.point([a.getGeometry().getExtent()[0],a.getGeometry().getExtent()[1]]),p=turf.point([a.getGeometry().getExtent()[2],a.getGeometry().getExtent()[3]]),f=turf.midpoint(d,p).geometry.coordinates,g=o();u.setPositioning(g),u.setPosition(f)}},h.closeInfo=function(){document.getElementById("info-popup").style.display="none"},h.visualizar=function(t){p.path("/geoidentificacao/"+t),setTimeout(function(){d.location.reload()},2e3)},setTimeout(function(){h.mviewer.viewer.map.getViewport().addEventListener("contextmenu",function(t){if(t.preventDefault(),h.geoidentificacao){var e=(new ol.format.WKT).readFeature(h.geoidentificacao.geomWkt),n=h.mviewer.viewer.map.getEventCoordinate(t);e.getGeometry().intersectsCoordinate(n)&&h.showInfoArea(h.geoidentificacao,n)}return!1})},4e3)})}t.$inject=["$scope","$rootScope","$mdToast","giService","olUtils","$mdDialog","giConfig","giUsuario","giLoading","giOlFunctions","$filter","$window","$location"],angular.module("su-geoidentificacao").controller("DemoCtrl",function(){this.topDirections=["left","up"],this.bottomDirections=["down","right"],this.isOpen=!1,this.availableModes=["md-fling","md-scale"],this.selectedMode="md-fling",this.availableDirections=["up","down","left","right"],this.selectedDirection="up"}).component("geoidentificacaoConsultar",{templateUrl:"scripts-geoidentificacao/geoidentificacao/geoidentificacao_consultar.html",controller:t})}();var overlayPopUPIntersecoesGeo,idG,idElemtentProd,idPro;!function(){"use strict";function t(t,e,n,r,o,i,a,s,c){function u(){map.getViewport().oncontextmenu=null,map.getViewport().oncontextmenu=function(t){var n=[];n.push(t.layerX),n.push(t.layerY);var r=map.getCoordinateFromPixel(n),o=map.forEachFeatureAtPixel(n,function(t){return t});if(o&&void 0!==o.get("tipoProcesso")&&"regular"===o.get("tipoProcesso")){var i=o.get("processo");e.$broadcast("popupConteudo",{responsePopup:i,coordinate:r})}return!1}}function l(){map.getViewport().oncontextmenu=null,map.getViewport().oncontextmenu=function(t){var r=[];r.push(t.layerX),r.push(t.layerY);var o=map.getCoordinateFromPixel(r),i=map.forEachFeatureAtPixel(r,function(t){return t});if(idG=i.getId(),i&&void 0!=i.getId()){var a=d.geometria;d.query.geomWKT=a,n.produtoCartografico.get({id:idG},function(t){e.$broadcast("popupConteudoIntersecoesGeo",{responsePopup:t}),overlayPopUPIntersecoesGeo.setPosition(o)})}return!1}}var d=t,p=e,f=this;d.authenticatedCadastrar=!0,d.niveisPrecisaoCartografica=[],d.tiposAcao=[],d.demandasGeradoras=[],d.geoidentificacaoUniao=!1,d.trochoIgualFim=!1,d.mostrarBotaoDesenhar=!1,d.mostrarBotaoUpload=!1,o.usuarioLogado(),function(){n.niveisPrecisaoCartograficasService.get(function(t){d.niveisPrecisaoCartografica=t.data})}(),o.usuarioLogado(function(){function t(t,e,n){y=a.confirm().title(t).textContent(e).ok("SIM").cancel("NÃO"),a.show(y).then(function(){return d.geometria=n,!0},function(){return d.geometria=null,!1})}function c(){var t=d.geoidentificacao.coTipoDemarcacao,n=d.geoidentificacao.coEtapaDemarcacao;null!=t&&null!=n?(e.$broadcast("showVetorizarGeometria",{show:!0}),e.$broadcast("showIncluirArquivo",{show:!0})):(e.$broadcast("showVetorizarGeometria",{show:!1}),e.$broadcast("showIncluirArquivo",{show:!1}))}if(-1!==p.usuario.permissoes.indexOf(o.cadastrar)){d.authenticatedCadastrar=!0;var h=[],g=[];p.selected=[],p.map=d,p.toggle=function(t){var e=p.selected,n=e.indexOf(t);n>-1?(e.splice(n,1),viewer.getLayer("vectorMetadadosIntercessoes").getSource().getFeatureById(t).setStyle(i.estiloRachurado)):(viewer.getLayer("vectorMetadadosIntercessoes").getSource().getFeatureById(t).setStyle(i.estiloRachuradoVermelho),e.push(t))},d.numeroProcessoInvalido=!1,d.toggle=p.toggle,p.exists=function(t){return p.selected.indexOf(t)>-1},d.exists=p.exists,d.ufsSelecionadas=[],d.municipiosSelecionados=[],d.geometry=null,d.listEtapaGeoidentificacao=[],d.geometadados=[],d.situacaoStatus=n.situacaoStatus.query(),d.mostrarLegenda=!1,d.estiloAreasCadastradasPorTipo={marinha:{cor:"#548235",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(84, 130, 53, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(84, 130, 53, 1)"})})},acrescidoMarinha:{cor:"#AAD08E",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(170, 208, 142, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(170, 208, 142, 1)"})})},marginal:{cor:"#FFD966",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(255, 217, 102, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(255, 217, 102, 1)"})})},acrescidoMarginal:{cor:"#F8CBAD",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(248, 203, 173, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(248, 203, 173, 1)"})})},suspenso:{cor:"#FF0000",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(0, 0, 0, 0)"}),stroke:new ol.style.Stroke({color:"rgba(255, 0, 0, 1)"})})},sobreposta:{cor:"#6600CC",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(102, 0, 204, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(102, 0, 204, 1)"})})},invalido:{estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(255, 0, 0, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(255, 0, 0, 1)"})})},outro:{cor:"#666666",estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:"rgba(102, 102, 102, 0.4)"}),stroke:new ol.style.Stroke({color:"rgba(102, 102, 102, 1)"})})},naoDemarcado:{estiloFeature:new ol.style.Style({fill:new ol.style.Fill({color:i.getHashedPattern()}),stroke:new ol.style.Stroke({color:"rgba(0, 0, 0, 1)"})})}},d.controleLegenda={marinha:!1,acrescidoMarinha:!1,marginal:!1,acrescidoMarginal:!1,suspenso:!1,sobreposta:!1,outro:!1,invalido:!1,produtoCartografico:!1,sobrepostaProdutosCartograficos:!1},d.tiposAreasCadastradas={MARINHA:"marinha",ACRESCIDO_MARINHA:"acrescidoMarinha",MARGINAL:"marginal",ACRESCIDO_MARGINAL:"acrescidoMarginal",SUSPENSO:"suspenso",SOPREPOSTA:"sobreposta",OUTRO:"outro",NOVO:"novo",INVALIDO:"invalido",PRODUTOS_CARTOGRAFICO:"produtoCartografico",SOBREPOSTA_PRODUTOS_CARTOGRAFICOS:"sobrepostaProdutosCartograficos"},d.adicionarAreaCadastrada=function(t,e,n){d.controleLegenda[e]=!0,"produtoCartografico"===e?e="outro":"sobrepostaProdutosCartograficos"===e&&(e="sobreposta");var r=d.estiloAreasCadastradasPorTipo[e].estiloFeature;t.setStyle(r),viewer.getLayer(n).getSource().addFeature(t)};var m=a.alert().clickOutsideToClose(!1).textContent().ok("OK");d.clearAlert=function(t){t.textContent(""),t.title("")};var y;d.geoidentificacao={},d.geoidentificacao.portariaComissaoDemarcacao=[{id:0}],d.obterTipoAreaCadastrada=function(t,e){var n=null;if(e)switch(e){case"regular":var r=t.tipoDemarcacao.noTipoDemarcacao;n="Terreno de Marinha"===r?d.tiposAreasCadastradas.MARINHA:"Terreno Marginal"===r?d.tiposAreasCadastradas.MARGINAL:d.tiposAreasCadastradas.OUTRO;break;case"acrescido":var r=t.processoDemarcatorio.tipoDemarcacao.noTipoDemarcacao;n="Terreno de Marinha"===r?d.tiposAreasCadastradas.ACRESCIDO_MARINHA:"Terreno Marginal"===r?d.tiposAreasCadastradas.ACRESCIDO_MARGINAL:d.tiposAreasCadastradas.OUTRO;break;case"suspenso":n=d.tiposAreasCadastradas.SUSPENSO;break;default:n=d.tiposAreasCadastradas.OUTRO}else n=d.tipoAreaCadastrada.OUTRO;return n},d.areasCadastradasRegularesAdicionadas=[],d.areasCadastradasAcrescidasAdicionadas=[],d.areasCadastradasSuspensasAdicionadas=[],d.$on("mapmoveend",function(t,e){var r=ol.geom.Polygon.fromExtent(e.extent),o=new ol.format.WKT,a=o.writeGeometry(r,{dataProjection:"EPSG:4326"}),s=i.getAproxAreaFrom4326WktCoordinates(a);d.bloquearBuscaAreasCadastradas||s<=2e3&&!d.zoomingOut&&(console.log("buscar areas cadastradas..."),d.bloquearBuscaAreasCadastradas=!0,n.buscarAreasCadastradasPorGeometria(a).then(function(t){if(200===t.status){var e,n=t.data;n.processosDemarcatorios.length>0&&(e="regular",d.adicionarAreaDeProcessoDemarcatorio(n.processosDemarcatorios,e)),n.processosDemarcatoriosAcrescidos.length>0&&(e="acrescido",d.adicionarAreaDeProcessoDemarcatorio(n.processosDemarcatoriosAcrescidos,e)),n.processosDemarcatoriosSuspensos.length>0&&(e="suspenso",d.adicionarAreaDeProcessoDemarcatorio(n.processosDemarcatoriosSuspensos,e)),u(),d.bloquearBuscaAreasCadastradas=!1}}))}),d.areaJaPresenteNoMapa=function(t,e){var n=[],r=null;"regular"===e?(n=d.areasCadastradasRegularesAdicionadas,r="idProcessoDemarcatorio"):"acrescido"===e?(r="id",n=d.areasCadastradasAcrescidasAdicionadas):"suspenso"===e&&(r="id",n=d.areasCadastradasSuspensasAdicionadas);for(var o=0;o<n.length;o++)if(n[o][r]===t[r])return!0;return!1},d.marcarAreaComoPresenteNoMapa=function(t,e){var n=[],r=null;"regular"===e?(n=d.areasCadastradasRegularesAdicionadas,r="idProcessoDemarcatorio"):"acrescido"===e?(r="id",n=d.areasCadastradasAcrescidasAdicionadas):"suspenso"===e&&(r="id",n=d.areasCadastradasSuspensasAdicionadas),n.push(t[r])},d.mapaPossuiAreasCadastradas=function(){var t=d.areasCadastradasRegularesAdicionadas,e=d.areasCadastradasAcrescidasAdicionadas,n=d.areasCadastradasSuspensasAdicionadas;return t.length>0||e.length>0||n.length>0},d.adicionarAreaDeProcessoDemarcatorio=function(t,e){d.inicializarCamadaAreasCadastradas();for(var n="acrescido"===e?"geomWktAcrescida":"geomWkt",r=new ol.format.WKT,o=0;o<t.length;o++){var i=t[o];if(!d.areaJaPresenteNoMapa(i,e)){var a=r.readFeature(i[n]);a.setId(i.idProcessoDemarcatorio),a.setProperties({processo:i,tipoProcesso:e});var s=d.obterTipoAreaCadastrada(i,e);if(d.adicionarAreaCadastrada(a,s,"vectorGeomCadastrados"),i.icSituacaoDemarcacao&&!0===i.icSituacaoDemarcacao){var c=a.clone();c.setProperties({processo:i,tipoProcesso:e}),d.adicionarAreaCadastrada(c,"naoDemarcado","vectorGeomCadastradosRachura")}d.marcarAreaComoPresenteNoMapa(i,e)}}},d.inicializarCamadaAreasCadastradas=function(){if(!viewer.getLayer("vectorGeomCadastrados")){var t=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorGeomCadastrados",t)}if(!viewer.getLayer("vectorGeomCadastradosRachura")){var e=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorGeomCadastradosRachura",e)}},d.legendaVisivel=function(){var t=!1;for(var e in d.controleLegenda)"naoDemarcado"!=e&&!0===d.controleLegenda[e]&&(t=!0);return t},d.limparAreasCadastradas=function(){viewer.getLayer("vectorGeomCadastrados")&&(viewer.getLayer("vectorGeomCadastrados").getSource().clear(),d.limparLegendaAreasCadastradas())},d.limparLegendaAreasCadastradas=function(){d.controleLegenda.marinha=!1,d.controleLegenda.acrescidoMarinha=!1,d.controleLegenda.marginal=!1,d.controleLegenda.acrescidoMarginal=!1,d.controleLegenda.suspenso=!1},d.limparLegendaAreasValidadas=function(){d.controleLegenda.invalido=!1,d.controleLegenda.sobreposta=!1,d.mapaPossuiAreasCadastradas()||d.limparLegendaAreasCadastradas()},d.query={geomWKT:null,size:4,page:1},d.ufs=n.uf.query(),n.tipoGeoidentificacao.get(function(t){d.listTipoGeoidentificacao=t.data}),n.etapaGeoidentificacao.get(function(t){t.data.forEach(function(t){"false"!==t.noEtapaDemarcacao&&d.listEtapaGeoidentificacao.push(t)})}),n.finalidadeGeoidentificacao.get(function(t){d.listFinalidadeGeoidentificacao=t.data}),d.changeSelectedTipo=function(t){c(),2!==t&&3!==t&&6!==t||(d.listEtapaGeoidentificacao=h),4!==t&&5!==t||(d.listEtapaGeoidentificacao=g),d.showEtapa=!0},d.changeSelectedEtapa=function(){c()},d.inicializarAnosOpcoesPortaria=function(){for(var t=(new Date).getFullYear(),e=[],n=t;n>=1950;n--)e.push(n);d.opcoesAnos=e},d.setarAnoPortaria=function(t,e){t.anoPortariaComissaoDemarcacao=e},d.inicializarAnosOpcoesPortaria(),d.mostrarDialogForaDoBrasil=function(t){y=a.confirm().title("Essa geometria está fora do País. Deseja continuar o cadastro dessa área da União?").ok("Sim").cancel("Não"),a.show(y).then(function(){d.validarGeometria(t)},function(){p.geometria=null,d.geometria=null,map.removeInteraction("draw")})},d.atualizarMunicipios=function(t){d.geoidentificacao.listMunString=[];for(var e=0;e<t.length;e++){var n=t[e];d.geoidentificacao.listMunString.push(n.geocodigo)}},d.$on("municipiosDefinidos",function(t,e){d.atualizarMunicipios(e)}),d.$on("geometriaDefinida",function(t,e){if("desenho"===e.tipo){d.geoidentificacao.niveisPrecisaoCartografica=d.niveisPrecisaoCartografica[1]}d.geoidentificacao.arquivoArea=e.arquivo,d.geoidentificacao.noArquivoArea=e.noArquivo,d.geoidentificacao.produtoCartRadio=null,d.validarGeometria(e.callback)}),d.$on("uploadfalhou",function(t,e){d.clearAlert(m),m.title(e),a.show(m)}),d.$on("geometriaForaDoBrasil",function(t,e){d.mostrarDialogForaDoBrasil(e.callback)}),d.resetarMapa=function(){viewer.getLayer("vectorCertos")&&viewer.getLayer("vectorCertos").getSource().clear(),viewer.getLayer("vectorCertosRachura")&&viewer.getLayer("vectorCertosRachura").getSource().clear(),viewer.getLayer("vectorIntercessoes")&&viewer.getLayer("vectorIntercessoes").getSource().clear(),viewer.getLayer("vectorErrados")&&viewer.getLayer("vectorErrados").getSource().clear(),viewer.getLayer("vectorGeomProdutosCadastrados")&&viewer.getLayer("vectorGeomProdutosCadastrados").getSource().clear(),viewer.getLayer("vectorProdutosIntercessoes")&&viewer.getLayer("vectorProdutosIntercessoes").getSource().clear(),viewer.getLayer("draw")&&viewer.getLayer("draw").getSource().clear(),d.limparLegendaAreasValidadas(),d.geometria=null,p.geometria=null,d.idsAreasCadastradas={},f.mapApi.limparMunicipios()},d.validarGeometria=function(r){if(d.geometriaValida=!1,map.removeEventListener("click"),p.geometria){viewer.getLayer("vectorCertos")&&viewer.getLayer("vectorCertos").getSource().clear(),viewer.getLayer("vectorCertosRachura")&&viewer.getLayer("vectorCertosRachura").getSource().clear(),viewer.getLayer("vectorIntercessoes")&&viewer.getLayer("vectorIntercessoes").getSource().clear(),viewer.getLayer("vectorErrados")&&viewer.getLayer("vectorErrados").getSource().clear();var o={coGeomAreaUniao:null,geomWkt:p.geometria,listMunString:d.geoidentificacao.listMunString,coEtapaDemarcacao:d.geoidentificacao.coEtapaDemarcacao};n.geoinformacaoValidarGeometria.save(o,function(e){var n=new ol.format.WKT;if(e.certos.length>0){var o=n.readFeature(e.certos.toString()),i=new ol.layer.Vector({source:new ol.source.Vector}),s=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorCertos",i),viewer.addLayer("vectorCertosRachura",s);var c;1===d.geoidentificacao.coTipoDemarcacao?c="marinha":2===d.geoidentificacao.coTipoDemarcacao&&(c="marginal"),d.adicionarAreaCadastrada(o,c,"vectorCertos");if(!d.geoidentificacao.icSituacaoDemarcacao||d.geoidentificacao.icSituacaoDemarcacao){var l=o.clone();d.adicionarAreaCadastrada(l,"naoDemarcado","vectorCertosRachura")}}if(e.intercessoes.length>0){var f=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorIntercessoes",f);for(var h=0;h<e.intercessoes.length;h++){var o=new ol.Feature({geometry:n.readFeature(e.intercessoes[h].intercessoes).getGeometry()});o.setId(e.intercessoes[h].id);var c="sobreposta";d.adicionarAreaCadastrada(o,c,"vectorIntercessoes")}}if(e.errados.length>0){var g=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorErrados",g);var o=n.readFeature(e.errados.toString()),c="invalido";d.adicionarAreaCadastrada(o,c,"vectorErrados")}if(-1!=e.status.indexOf("2"))t("Atenção!",e.msg.toString(),p.geometria);else if("1"==e.status.toString())d.clearAlert(m),m.textContent(e.msg.toString()).title("Erro!"),a.show(m),d.geometria=null;else{d.clearAlert(m);var y=e.msg.toString();if(e.intercessoes.length>0){var v="Existe uma sobreposição de Geoidentiicação de Área da União nesta Região.";y=y?y+" "+v:v}m.textContent(y).title("Geometria validada com sucesso!"),a.show(m),d.geometriaValida=!0,d.geometria=p.geometria,d.areaAproximadaEmKilometros=p.areaAproximada,d.showProdCartEmAreaUniao=!0}r(),u()}),map.removeInteraction(draw)}else e.$broadcast("showLegenda",{show:!1}),d.clearAlert(m),m.textContent("não há geometria"),a.show(m);e.$broadcast("showLegenda",{show:!0})},d.verificarDivergenciaDeArea=function(){Math.abs(Math.floor(d.areaAproximadaEmKilometros/d.areaOficialEmKilometros*100)-100)>10&&(y=a.confirm().title("A Área Oficial informada é muito diferente da área do polígono demarcado. Deseja continuar?").ok("Sim").cancel("Não"),a.show(y).then(function(){},function(){d.geoidentificacao.areaOficial=null}))},d.changeSituacao=function(){d.geoidentificacao.icSituacaoDemarcacao?d.showSuspensao=!1:d.showSuspensao=!0},d.filtroPaginacaoProduto=function(t,e){var n=d.produtosCartograficos.quantidade,r=d.produtosCartograficos.pagina;if(n&&r){var o=0+n*(r-1),i=r*n-1;return e>=o&&e<=i}return!0},d.buscarProdutos=function(t){!0===t&&p.geometria?(d.produtosCartograficos={pagina:1,quantidade:5,order:"idProdutoCartografico",total:0,data:[]},d.buscaProdutosCartograficos=n.produtosCartograficosIntercedidos(p.geometria,{paginacao:{page:null,size:null}}),d.buscaProdutosCartograficos.then(function(t){if(200===t.status){if(viewer.getLayer("vectorProdutosCartograficos"))viewer.getLayer("vectorProdutosCartograficos").getSource().clear();else{var e=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorProdutosCartograficos",e)}if(viewer.getLayer("vectorIntercessoesProdutosCartograficos"))viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().clear();else{var r=new ol.layer.Vector({source:new ol.source.Vector});viewer.addLayer("vectorIntercessoesProdutosCartograficos",r)}var o=t.data,i=o.content;d.tabelaCatalogoMetadados=[];var a=new ol.format.WKT;angular.forEach(i,function(t,e){var r=t.meta;if(r&&r.idProdutoCartografico){var o={id:null,titulo:null,numeroProcesso:null,nomeCadastrador:null,situacao:null,data:null,relacionado:!1};if(o.id=r.idProdutoCartografico,o.titulo=r.noTitulo,null!=r.coSituacaoStatus){var i=r.coSituacaoStatus;angular.forEach(d.situacaoStatus,function(t,e){t.idSituacaoStatus==i&&(o.situacao=t.noSituacaoStatus)})}if(n.geoidentificacaoNumeroProcesso.get({id:r.coGeoidentificacao},function(t){o.numeroProcesso=t.content}),o.nomeCadastrador=r.coCadastrador,o.data=r.dtCriacaoIdentificacao,d.produtosCartograficos.data.push(o),null!=t.geomProdutoWKT){var s=new ol.Feature({geometry:a.readFeature(t.geomProdutoWKT).getGeometry()});s.setId(r.idProdutoCartografico);var c="produtoCartografico";d.adicionarAreaCadastrada(s,c,"vectorProdutosCartograficos")}if(null!=t.intersecaoWkt){var s=new ol.Feature({geometry:a.readFeature(t.intersecaoWkt).getGeometry()});s.setId(r.idProdutoCartografico);var c="sobrepostaProdutosCartograficos";d.adicionarAreaCadastrada(s,c,"vectorIntercessoesProdutosCartograficos")}}}),0===d.produtosCartograficos.data.length?s.show({position:"bottom right",template:'<md-toast><div class="md-toast-content">Nenhum registro encontrado</div></md-toast>'}):l(),d.showJustificativaProdCart=!1,d.showAccordionProdCart=!0,d.requiredJustificativa=!1}})):(d.showJustificativaProdCart=!0,d.showAccordionProdCart=!1,d.requiredJustificativa=!0,d.removeProdutosCartograficos())},d.removeProdutosCartograficos=function(){viewer.getLayer("vectorProdutosCartograficos")&&viewer.getLayer("vectorProdutosCartograficos").getSource().clear(),viewer.getLayer("vectorIntercessoesProdutosCartograficos")&&viewer.getLayer("vectorIntercessoesProdutosCartograficos").getSource().clear(),d.controleLegenda.produtoCartografico=!1,d.controleLegenda.sobrepostaProdutosCartograficos=!1},d.checkSuspensao=function(){return!d.geoidentificacao.icSituacaoDemarcacao},d.submit=function(){if(d.geometria)if(d.geoidentificacaoForm.$valid){if(d.geoidentificacao.icAtivo=!0,d.geoidentificacao.geomWkt=d.geometria,d.geoidentificacao.coCadastrador=p.usuario.cpf,d.geoidentificacao.areaAproximada=Math.ceil(1e6*d.areaAproximadaEmKilometros),d.geoidentificacao.areaOficial=Math.ceil(1e6*d.areaOficialEmKilometros),d.produtosCartograficos&&d.produtosCartograficos.data){d.geoidentificacao.coProdutosCartograficosRelacionados=[];for(var t=0;t<d.produtosCartograficos.data.length;t++)d.produtosCartograficos.data[t].relacionado&&d.geoidentificacao.coProdutosCartograficosRelacionados.push(d.produtosCartograficos.data[t].id)}"true"==d.geoidentificacao.produtoCartRadio?d.geoidentificacao.dsJustificativaGeometadados="":p.selected=[],n.geoinformacaoGravar.save(d.geoidentificacao,function(t){if(0!=p.selected.length){var e={geoIdt:t.idProcessoDemarcatorio,listProd:p.selected};n.processoDemarcatorioProd.save(e,function(e){r.path("/geoidentificacao/editar/"+t.idProcessoDemarcatorio)})}else r.path("/geoidentificacao/editar/"+t.idProcessoDemarcatorio)})}else d.clearAlert(m),m.textContent("Formulário Inválido!"),m.title("Erro!"),a.show(m);else d.clearAlert(m),m.textContent("Uma geometria precisa ser cadastrada!"),m.title("Erro!"),a.show(m)},d.reset=function(){d.geoidentificacao.nuProcessoDemarcatorio="",d.geoidentificacao.coTipoDemarcacao="",d.geoidentificacao.coEtapaDemarcacao="",d.geoidentificacao.coFinalidadeDemarcacao="",d.geoidentificacao.icSituacaoDemarcacao="",d.geoidentificacao.dsMotivoSuspensa="",d.geoidentificacao.dsTrechoInicio="",d.geoidentificacao.dsTrechoFim="",d.geoidentificacao.dsObservacao="",d.geoidentificacao.geomWkt=null,d.geoidentificacao.produtosRelacionados=[],d.geoidentificacao.produtoCartRadio=null,d.geoidentificacao.niveisPrecisaoCartografica=null,d.areaOficialEmKilometros=null,d.areaAproximadaEmKilometros=null,d.resetarMapa()},d.atualizarBotoesDoMapa=function(){d.geoidentificacao.coEtapaDemarcacao&&d.geoidentificacao.coTipoDemarcacao&&(d.geoidentificacao.coEtapaDemarcacao<=3?(d.mostrarBotaoDesenhar=!0,d.mostrarBotaoUpload=!0):(d.mostrarBotaoDesenhar=!1,d.mostrarBotaoUpload=!0));var t=d.geoidentificacao.coEtapaDemarcacao;1===t||2===t||3===t?(d.geoidentificacao.icSituacaoDemarcacao=!1,d.geoidentificacao.dsSituacaoDemarcacao="Não Demarcado"):(d.geoidentificacao.icSituacaoDemarcacao=!0,d.geoidentificacao.dsSituacaoDemarcacao="Demarcado")}}}),d.consultaProcessoSei=function(){d.geoidentificacao.nuProcessoDemarcatorio&&n.consultaProcessoSei.save({numeroProcesso:d.geoidentificacao.nuProcessoDemarcatorio},function(t){if(null==t.idProcedimento){var e=a.alert().clickOutsideToClose(!1).textContent("Nº do Processo não encontrado").ok("OK");a.show(e),d.numeroProcessoInvalido=!0}else d.numeroProcessoInvalido=!1},function(){var t=a.alert().clickOutsideToClose(!1).textContent("Nº do Processo não encontrado").ok("OK");a.show(t),d.numeroProcessoInvalido=!0,console.error("Falha ao consultar processo SEI")})},d.addPortaria=function(){d.geoidentificacao.portariaComissaoDemarcacao.push({id:d.geoidentificacao.portariaComissaoDemarcacao.length+1})},d.removePortaria=function(t){d.geoidentificacao.portariaComissaoDemarcacao.splice(d.geoidentificacao.portariaComissaoDemarcacao.indexOf(t),1)},d.cancelar=function(){r.path("/geoidentificacao"),setTimeout(function(){c.location.reload()},1e3)}}t.$inject=["$scope","$rootScope","giService","$location","giUsuario","giOlFunctions","$mdDialog","$mdToast","$window"],angular.module("su-geoidentificacao").component("geoidentificacaocadastrar",{templateUrl:"scripts-geoidentificacao/geoidentificacao/geoidentificacao_cadastrar.html",controller:t}).directive("manuallyOpened",[function(){return{restrict:"A",scope:{opened:"="},link:function(t,e,n){t.$watch(n.opened,function(t){!0===t?e[0].click():n.opened=!1})}}}])}(),function(){"use strict";angular.module("su-geoidentificacao").factory("utils_factory",function(){function t(t){const e=/[A-Z]/;return t=t.toUpperCase(),t=t.replace(/[^A-Z0-9]/g,""),t.length<=13&&!e.test(t)?(t=t.replace(/(\d{3})(\d)/,"$1.$2"),t=t.replace(/(\d{3})(\d)/,"$1.$2"),t=t.replace(/(\d{3})(\d{1,2})$/,"$1-$2")):(t=t.replace(/^([A-Z0-9]{2})([A-Z0-9])/,"$1.$2"),t=t.replace(/^([A-Z0-9]{2})\.([A-Z0-9]{3})([A-Z0-9])/,"$1.$2.$3"),t=t.replace(/\.([A-Z0-9]{3})([A-Z0-9])/,".$1/$2"),t=t.replace(/([A-Z0-9]{4})([A-Z0-9])/,"$1-$2")),t}function e(t){return t=t.replace(/D/g,""),t=t.replace(/^(\d{5})(\d)/,"$1-$2")}function n(t){return t=t.replace(/\D/g,""),t=t.replace(/^(\d\d)(\d)/g,"($1) $2"),t=t.replace(/(\d{4})(\d)/,"$1-$2")}return{mascaraCpfCnpj:t,mascaraCep:e,mascaraTelefone:n}})}(),function(){"use strict";angular.module("su-geoidentificacao").factory("giUsuario",["$rootScope","giService","$mdDialog",function(t,e,n){function r(n){return e.usuario.get(function(e){i.usuario=e,t.$broadcast("auth_geoidentificacao"),n&&angular.isFunction(n)&&n()})}function o(t){var e=n.alert().clickOutsideToClose(!1).textContent("O usuário "+i.usuario.nome+" não possui permissão para "+t+".").ok("OK");n.show(e)}var i=t;return{usuarioLogado:r,alertPermissao:o,cadastrar:"GEOIDENTIFICACAO.CADASTRAR_GEOIDENTIFICACAO",consultar:"GEOIDENTIFICACAO.CONSULTAR_GEOIDENTIFICACAO",editar:"GEOIDENTIFICACAO.EDITAR_GEOIDENTIFICACAO",excluir:"GEOIDENTIFICACAO.EXCLUIR_GEOIDENTIFICACAO"}}])}(),function(){"use strict";angular.module("su-geoidentificacao").factory("giService",["$resource","$http","giConfig",function(t,e,n){var r=function(t,r){return e.post(n.apiUrl+"geoinformacao/intersecoes/produtos-cartograficos-meta",{geometria:t},{params:{page:r.pagina,size:r.quantidade}})},o=function(t){return e.post(n.apiUrl+"geoinformacao/areasCadastradas",{geomWkt:t})},i=function(t,r){return e.post(n.apiUrl+"geoinformacao/obterAreasSobrepostas",{geom:t,geomInteserdidas:r})},a=function(t){return e.post(n.apiUrl+"/geoinformacao/municipios-geo-coincidentes",t)},s=function(t){return e.get(n.apiUrl+"/geoinformacao/areaAcrescidaPorProcessoDemarcatorio/"+t)},c=function(t){return e.get(n.apiUrl+"/geoinformacao/areasSuspensasPorIdProcessoDemarcatorio/"+t)};return{usuario:t(n.apiUrl+"cliente/usuario-logado"),geoidentificacao:t(n.apiUrl+"geoinformacao"),geoidentificacaoGeoms:t(n.apiUrl+"geoinformacao/geom-id"),geoidentificacaoNumeroProcesso:t(n.apiUrl+"geoinformacao/:id/numero-processo"),geoinformacaoGravar:t(n.apiUrl+"geoinformacao/gravar"),geoinformacaoEditar:t(n.apiUrl+"geoinformacao/editar"),geoinformacaoPesquisar:t(n.apiUrl+"geoinformacao/pesquisar"),geoinformacaoValidarGeometria:t(n.apiUrl+"geoinformacao/validarGeometria"),geoinformacao:t(n.apiUrl+"geoinformacao/:id"),uf:t(n.geoservicosUrl+"unidade-federacao"),idUF:t(n.geoservicosUrl+"unidade-federacao/:id"),municipioSimples:t(n.geoservicosUrl+"municipio/simples"),municipio:t(n.geoservicosUrl+"municipio/:idUF"),municipioPorGeocodigoMunicipio:t(n.geoservicosUrl+"municipio/info/:geocodigoMunicipio"),regiao:t(n.geoservicosUrl+"regiao/:id"),appParametros:t(n.apiUrl+"app/parametros"),transforma:t(n.geoservicosUrl+"transformacao/geometria"),ufPorRegiao:t(n.geoservicosUrl+"/regiao/:idRegiao/unidade-federacao"),tipoGeoidentificacao:t(n.apiUrl+"dominios/listarTodasTipoDemarcacao"),etapaGeoidentificacao:t(n.apiUrl+"dominios/listarTodasEtapaDemarcacao"),finalidadeGeoidentificacao:t(n.apiUrl+"dominios/listarTodasFinalidadeDemarcacao"),excluirGeoidentificacao:t(n.apiUrl+"geoinformacao/excluir/:id"),pesquisarIntercedidas:t(n.geometadadosUrl+"catalogo/produtos-cartograficos/list"),situacaoStatus:t(n.geometadadosUrl+"metadados/buscar/situacaostatus"),processoDemarcatorioProd:t(n.geometadadosUrl+"catalogo/processoDemarcatorioProd/"),geometadadosPorProcesso:t(n.geometadadosUrl+"catalogo/produtos-cartograficos/processo"),geometadadosTituloProdCart:t(n.geometadadosUrl+"catalogo/produtos-cartograficos/titulos/numProcesso/:numProcesso"),
produtoCartograficoPorIdCoGeoidentificacao:t(n.geometadadosUrl+"catalogo/produtos-cartograficos/buscar/:coGeoidentificacao"),cadastradorSelect:t(n.apiUrl+"geoinformacao/cadastradores"),produtoCartografico:t(n.geometadadosUrl+"catalogo/produtos-cartograficos/:id"),buscarHistorico:t(n.apiUrl+"geoinformacao/buscarHistorico"),proxyGeoserver:t(n.apiUrl+"public/proxy"),niveisPrecisaoCartograficasService:t(n.apiUrl+"dominios/niveisPrecisaoCartograficas"),consultaDocumentoSei:t(n.apiUrl+"public/consultaDocumento/:documento"),consultaProcessoSei:t(n.apiUrl+"public/consultaProcesso"),listarTiposSuspensoes:t(n.apiUrl+"dominios/listarTiposSuspensoes/:coEtapa/:icSituacao"),gravarSuspensao:t(n.apiUrl+"geoinformacao/gravarSuspensao"),getSuspensao:t(n.apiUrl+"geoinformacao/getSuspensao/:id"),pageSuspensao:t(n.apiUrl+"geoinformacao/getPageSuspensao"),pageSuspensaoArquivos:t(n.apiUrl+"geoinformacao/getPageSuspensaoArquivo"),removerSuspensao:t(n.apiUrl+"geoinformacao/removerSuspensao/:id"),excluirSuspensaoArquivo:t(n.apiUrl+"geoinformacao/excluirSuspensaoArquivo/:id"),pageArquivoAcrescido:t(n.apiUrl+"geoinformacao/getPageArquivoAcrescido"),excluirArquivoAcrescido:t(n.apiUrl+"geoinformacao/excluirArquivoAcrescido/:id"),gravarArquivoAcrescido:t(n.apiUrl+"geoinformacao/gravarArquivoAcrescido"),municipiosPorGeometria:t(n.apiUrl+"/geoinformacao/municipios-geo-coincidentes"),produtosCartograficosIntercedidos:r,buscarAreasCadastradasPorGeometria:o,buscarSobreposicoesPorGeometria:i,buscarMunicipiosPorGeometria:a,buscarAreaAcrescidaPorProcessoDemarcatorio:s,buscarAreasSuspensasPorProcessoDemarcatorio:c}}])}();var editar=!1,visualizar=!1;!function(){"use strict";angular.module("su-geoidentificacao").component("app",{templateUrl:"scripts-geoidentificacao/main.html",controller:["$scope","Service",function(t,e){t.usuario=e.usuario.get()}]})}(),angular.module("su-geoidentificacao").run(["$templateCache",function(t){t.put("scripts-geoidentificacao/main.html",""),t.put("scripts-geoidentificacao/map/map.html",'<div id="map" style="top:0;left: 0; width: 100%;  height: 100%; position: relative;">\n    <div class="ol-search-wrapper">\n        <form novalidate>\n            <div layout="row">\n                <input flex="nogrow" type="text" class=".ol-input-search" ng-model="searchQuery" placeholder="Buscar">\n                <input type="button" class="ol-go-button-search" value="Ir" ng-click="executeQuery()">\n            </div>\n        </form>\n    </div>\n</div>\n\n<div ng-show="showPopupIntersecoesGeo" id="popupIntersecoes" class="ol-popup">\n    <a href="#" id="popup-closerIntersecoes" class="ol-popup-closer" ng-click="close()"></a>\n    <div id="popup-contentIntersecoes">\n        <label><b>Produto Cartográfico</b></label><br>\n        <label><b>Titulo do Produto Cartográfico: </b>{{popup.noTitulo}}</label><br>\n        <label><b>Data do Produto Cartográfico: </b>{{popup.dtCriacaoMetadado}}</label><br>\n        \x3c!--       \t  <input id="checkProdutoCart" class="{{item.idProdutoCartografico}}"  ng-click="checkPopup()" type="checkbox"> --\x3e\n        \x3c!-- <label><b>Esta Geoidentificação de Áreas da União possui produtos cartográficos associados a ela?</b></label> --\x3e\n    </div>\n</div>\n\n<ng-transclude></ng-transclude>\n\n\x3c!-- <div style="display: flex" layout-align="center center" ng-show="showLegenda">\n    <b>LEGENDA: </b>&emsp;\n    <div class="retangulo cinza"></div>&ensp; Geometria Cadastrada &emsp;\n    <div class="retangulo amarelo"></div>&ensp; Geometria Nova (não cadastrada)&emsp;\n    <div class="retangulo vermelho"></div>&ensp; Geometria Inconsistente&emsp;\n    <div class="retangulo roxo"></div>&ensp; Geometria sobreposta&emsp;\n</div> --\x3e\n\n<div id="popup" class="ol-popup">\n    <a href="#" id="popup-closer" class="ol-popup-closer"></a>\n    <div id="popup-content"></div>\n</div>\n\n<div layout="row" layout-align="space-around center">\n    <div flex="33" style="display: flex; margin-bottom: 180px">\n        <div layout-align="center center" ng-show="mostrarBotaoDesenhar === true">\n            <md-button style="width: 210px" class="md-primary md-raised" aria-label="Desenhar Polígono" ng-click="drawMap()">Desenhar Geometria\n                <md-tooltip md-direction="bottom">\n                    DESENHAR GEOMETRIA\n                </md-tooltip>\n                \x3c!-- \t    <md-icon md-svg-src="assets/img/icons/ic_stop_white_24px.svg"></md-icon> --\x3e\n            </md-button>\n        </div>\n\n        <div ng-show="mostrarBotaoUpload === true" ng-cloack layout="row" layout-align="center center">\n            <md-button style="width: 210px" ngf-select="uploadFile($file)" class="md-primary md-raised" aria-label="Incluir Arquivo">Importar Geometria\n                <md-tooltip md-direction="bottom">\n                    O arquivo shapefile deve estar compactado em um arquivo .zip. O arquivo .zip deve conter pelo menos os componentes dos arquivos .shp, .shx, dbf e .prj do shapefile. <br> O arquivo shapefile deve ser apresentado com DATUM SIRGAS 2000 em coordenadas geograficas\n                    (Código EPSG: 4674).\n                </md-tooltip>\n                \x3c!-- \t    <md-icon md-svg-src="assets/img/icons/ic_file_upload_white_36px.svg"></md-icon> --\x3e\n            </md-button>\n        </div>\n    </div>\n    <div flex="66" ng-show="geometria != null" style="padding: 5px;">\n        <div class="table-ufs">\n            <md-table-container>\n                <table>\n                    <thead>\n                        <tr>\n                            <th class="label-ufs-table"><b>UF(s):</b></th>\n                            <th class="label-municipio-table"><b>Municípios(s):</b></th>\n                        </tr>\n                    </thead>\n                    <tbody>\n\n                        <tr ng-repeat="municipio in municipios">\n                            <td>{{ municipio.uf }} </td>\n                            <td>{{ municipio.nome }} </td>\n                        </tr>\n                    </tbody>\n                </table>\n            </md-table-container>\n        </div>\n    </div>\n</div>\n\n\n\x3c!-- <div ng-show="mostrarBotaoUpload === true">\n  <fieldset>\n    <legend>Incluir Arquivo:</legend>\n    <upload></upload>\n  </fieldset>\n</div> --\x3e'),t.put("scripts-geoidentificacao/su/navbar.html",'\n\x3c!-- Barra de navageção --\x3e\n<md-toolbar>\n  <div class="md-toolbar-tools">\n\n    \x3c!-- hide-gt-sm --\x3e\n    <md-button class="" ng-click="toggleMenu()" aria-label="Menu da Aplicação">\n      <md-icon>menu\n    </md-icon></md-button>\n\n    <h2>\n      <img src="assets/img/logo-spu.png">\n    </h2>\n\n    <span flex></span>\n\n    <md-button class="md-icon-button">\n      <md-icon alt="Home">\n        home\n        <md-tooltip md-direction="bottom">\n          Página inicial\n        </md-tooltip>\n      </md-icon>\n    </md-button>\n\n    <md-button class="md-icon-button">\n      <md-icon alt="Notificações">\n        notifications\n        <md-tooltip md-direction="bottom">\n          Notificações\n        </md-tooltip>\n      </md-icon>\n    </md-button>\n\n    <md-button class="md-icon-button">\n      <md-icon alt="Ajuda">\n        help\n        <md-tooltip md-direction="bottom">\n          Ajuda\n        </md-tooltip>\n      </md-icon>\n    </md-button>\n\n    <md-button class="md-fab md-primary md-hue-2" ng-click="$mdOpenMenu($event)" aria-label="Configurações">\n      <md-icon style="font-size: 24px;">person</md-icon>\n      <md-tooltip md-direction="left">\n        Configurações da aplicação\n      </md-tooltip>\n    </md-button>\n\n  </div>\n\n</md-toolbar>\n\n<md-toolbar class="md-whiteframe-z3" style="min-height: 32px !important; background: #38813C !important;">\n  <div class="md-toolbar-tools" style="height: 32px;">\n    <div flex="60" ncy-breadcrumb></div>\n    <div style="text-align: right;"></div>\n    <div flex layout="row" layout-align="end center">\n      <p style="font-size: 10px">Sua sessão expira em 00:40:00</p>\n    </div>\n  </div>\n</md-toolbar>\n\n\x3c!-- MENU --\x3e\n<menu></menu>\n'),t.put("scripts-geoidentificacao/su_map/su_map.html",'<div ng-attr-id="{{mapid}}" class="su-map">\n  <div class="ol-search-wrapper">\n    <form novalidate>\n      <div layout="row" ng-show="mostrarQuery">\n        <input flex="nogrow" type="text" class=".ol-input-search" ng-model="searchQuery" placeholder="Buscar">\n        <input type="button" class="ol-go-button-search" value="Ir" ng-click="executeQuery()">\n      </div>\n    </form>\n  </div>\n</div>\n<div ng-transclude></div>\n<div ng-show="showPopupIntersecoesGeo" id="popupIntersecoes" class="ol-popup">\n  <a href="#" id="popup-closerIntersecoes" class="ol-popup-closer" ng-click="close()"></a>\n  <div id="popup-contentIntersecoes">\n    <label><b>Produto Cartográfico</b></label><br>\n    <label><b>Titulo do Produto Cartográfico: </b>{{popup.noTitulo}}</label><br>\n    <label><b>Data do Produto Cartográfico: </b>{{popup.dtCriacaoMetadado}}</label><br>\n  </div>\n</div>\n\n<div ng-show="popup.showPopup" id="popup" class="ol-popup">\n  <a href="#" id="popup-closer" class="ol-popup-closer"></a>\n  <div id="popup-content">\n      <p> <strong> Nº do Processo: </strong> {{popup.nuProcessoDemarcatorio}} </p>\n      <p> <strong> Tipo: </strong> {{popup.tipo}} </p>\n      <p> <strong> Trecho: </strong> {{popup.trecho}} </p>\n      <div ng-if="popup.portariaComissaoDemarcacao">\n          <p> <strong> Portaria de comissão de demarcação: </strong> </p>\n          <p ng-if="popup.portariaComissaoDemarcacao" ng-repeat="portaria in popup.portariaComissaoDemarcacao">\n              {{ portaria.nuPortariaComissaoDemarcacao + \'/\' + portaria.anoPortariaComissaoDemarcacao}}\n          </p>\n      </div>\n      <p> <strong> Etapa: </strong> {{popup.etapa}} </p>\n      <p> <strong> Situação: </strong> {{popup.situacao}} </p>\n      <p> <strong> Demanda geradora: </strong> {{popup.demandaGeradora}} </p>\n      <p> <strong> UF(s): </strong> {{popup.ufs}} </p>\n      <p> <strong> Municípios(s): </strong> {{popup.municipios}} </p>\n      <p> <strong> Área aproximada: </strong> {{popup.areaAproximada}} </p>\n      <p> <strong> Nível de precisão: </strong> {{ popup.nivelPrecisaoCartografia }} </p>\n      <p> <strong> Cadastrador: </strong> {{popup.noCadastrador}}</p>\n      <p> <strong> Data de cadastro: </strong> {{ popup.dtCadastro }} </p>\n  </div>\n</div>\n'),t.put("scripts-geoidentificacao/upload/upload.html",'<div ng-controller="myCtrl">\n  <input type="file" file-model="myFile" enctype="multipart/form-data">\n  <br>\n  <md-button class="md-raised md-primary" ng-click="uploadFile()">Gravar</md-button>\n</div>\n'),t.put("scripts-geoidentificacao/geoidentificacao/geoidentificacao_cadastrar.html",'<style>\n    .legendas-mapa {\n        display: flex;\n        margin-left: 46px;\n    }\n    \n    .botoes-geometria {\n        display: flex;\n        margin-top: 50px;\n    }\n    \n    .h4-input-date {\n        font-weight: normal;\n        margin: auto;\n    }\n    \n    .items-td-list {\n        margin-top: 5px;\n        font-weight: bold;\n    }\n    \n    .label-titulo {\n        margin-left: 5px;\n    }\n    \n    .label-value {\n        font-weight: normal;\n    }\n</style>\n\n<link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">\n\n<div ng-show="authenticatedCadastrar" id="cadastro-geoidentificacao">\n    <form name="geoidentificacaoForm" ng-submit="submit()" novalidate>\n        <h2 class="su-geoidentificacao-texto-principal">Cadastrar Geoidentificação de Áreas da União</h2>\n\n        \n\n        <div layout-align="center center" layout-margin layout-wrap style="min-height: 500px; margin-bottom: 20px;">\n            <gi-map geometry="geometry" mostrar-botao-upload="mostrarBotaoUpload" mostrar-botao-desenhar="mostrarBotaoDesenhar" on-init-map="$ctrl.mapApi = $API;">\n                <div style="display: flex; position: relative;" layout-align="center center" ng-if="legendaVisivel()">\n                    <b>LEGENDA:</b>&emsp;\n                    <div ng-if="controleLegenda.marinha">\n                        <div class="retangulo marinha"></div>&ensp; Terreno de Marinha &emsp;\n                    </div>\n                    <div ng-if="controleLegenda.acrescidoMarinha">\n                        <div class="retangulo acrescidoMarinha"></div>&ensp; Acrescido de Marinha&emsp;\n                    </div>\n                    <div ng-if="controleLegenda.marginal">\n                        <div class="retangulo marginal"></div>&ensp; Terreno Marginal&emsp;\n                    </div>\n                    <div ng-if="controleLegenda.acrescidoMarginal">\n                        <div class="retangulo acrescidoMarginal"></div>&ensp; Acrescido Marginal&emsp;\n                    </div>\n                    <div ng-if="controleLegenda.suspenso">\n                        <div class="retangulo suspenso"></div>&ensp; Área suspensa&emsp;\n                    </div>\n                    <div ng-if="controleLegenda.sobreposta || controleLegenda.sobrepostaProdutosCartograficos">\n                        <div class="retangulo sobreposta"></div>&ensp; Área sobreposta&emsp;\n                    </div>\n                    <div ng-if="controleLegenda.invalido">\n                        <div class="retangulo invalido"></div>&ensp; Área inválida &emsp;\n                    </div>\n                    <div ng-if="controleLegenda.outro || controleLegenda.produtoCartografico">\n                        <div class="retangulo outro"></div>&ensp; Outros &emsp;\n                    </div>\n                </div>\n            </gi-map>\n        </div>\n\n        <div layout="column">\n            <div layout="row" style="margin-top: -200px">\n                <md-content layout-gt-sm="row" layout-padding>\n                    <md-input-container md-is-error="numeroProcessoInvalido">\n                        <label>Nº do Processo de identificação</label>\n                        <input ng-model="geoidentificacao.nuProcessoDemarcatorio" name="nuProcessoDemarcatorio" size="50" onkeypress="if(this.value.length == 254) return false" md-maxlength="254" ng-blur="consultaProcessoSei()">\n\n                        <div ng-messages="geoidentificacaoForm.nuProcessoDemarcatorio.$error">\n                            <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                        </div>\n                    </md-input-container>\n                </md-content>\n            </div>\n            <div layout="row">\n                <md-content layout-gt-sm="row" layout-padding>\n                    <md-input-container style="width: 425px">\n                        <label>Nível de Precisão Cartográfica</label>\n                        <md-select name="nivelPrecisaoCartografica" ng-model="geoidentificacao.niveisPrecisaoCartografica" ng-disabled="geoidentificacao.niveisPrecisaoCartografica.idNiveisPrecisaoCartografica === 2" ng-model-options="{trackBy: \'$value.idNiveisPrecisaoCartografica\' }">\n                            <md-option ng-repeat="niveil in niveisPrecisaoCartografica" ng-value="niveil">\n                                {{niveil.dsNiveisPrecisaoCartografica}}\n                                <md-tooltip md-direction="top">Indica o nível de precisão de uma feição obtida a partir de um Produto Cartográfico.\n                                </md-tooltip>\n                            </md-option>\n                        </md-select>\n                    </md-input-container>\n                </md-content>\n            </div>\n        </div>\n\n        <md-content ng-if="geometria != null && geometriaValida === true" layout="column">\n            <p>Essa Geoidentificação de Áreas da União possui produtos cartográficos associados a ela?</p>\n            <div layout="row" layout-align="start start" layout-xs="column" layout-align-xs="start start" layout-margin="">\n                <md-input-container>\n                    <md-radio-group layout="row" ng-model="geoidentificacao.produtoCartRadio" ng-change="buscarProdutos(geoidentificacao.produtoCartRadio)">\n                        <md-radio-button style="width: 93px" ng-value="{{ true }}" class="md-primary">Sim</md-radio-button>\n                        <md-radio-button ng-value="{{ false }}" class="md-primary"> Não</md-radio-button>\n                    </md-radio-group>\n                </md-input-container>\n\n                <md-input-container ng-show="showJustificativaProdCart" style="width:648px;" flex="none">\n                    <label>Justificativa</label>\n                    <input ng-model="geoidentificacao.dsJustificativaGeometadados" name="dsJustificativaGeometadados" md-maxlength="200" ng-required="showJustificativaProdCart">\n                    <div ng-messages="geoidentificacaoForm.dsJustificativaGeometadados.$error">\n                        <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                    </div>\n                </md-input-container>\n            </div>\n        </md-content>\n\n        \x3c!-- <md-table-container ng-show="geoidentificacao.produtoCartRadio == \'true\'">\n    <table md-table ng-model="produtosCartograficos">\n      <tbody md-body>\n      <tr md-row ng-repeat="item in tabelaCatalogoMetadados">\n        <td md-cell class="shine">\n          <md-checkbox ng-click="toggle(item.idProdutoCartografico);" ng-checked="exists(item.idProdutoCartografico)">\n          </md-checkbox>\n        </td>\n\n        <td md-cell class="shine">\n          <p style="padding: 0;">\n            <span style="font-weight: bold;">Titulo:</span>\n            {{item.noTitulo}}\n          </p>\n\n          <p style="padding: 0">\n            <span style="font-weight: bold;">Nº do Processo de Identificação:</span>\n\n            <span ng-if="item.coGeoidentificacao && item.nuProcessoDemarcatorioString" style="font-weight: bold;">\n                      {{item.nuProcessoDemarcatorioString}}\n                    </span>\n\n            <i ng-if="item.coGeoidentificacao && !item.nuProcessoDemarcatorioString"\n               class="material-icons" style="color: orange;">\n              report_problem\n              <md-tooltip md-direction="top"> Este produto\n                cartográfico está vinculado a Geoidentificação de\n                Áreas da União sem número de processo</md-tooltip>\n            </i>\n\n            <i ng-if="!item.coGeoidentificacao"\n               class="material-icons" style="color: red;">report_problem\n              <md-tooltip md-direction="top"> Este produto\n                cartográfico não está vinculado a nenhuma Geoidentificação de\n                Áreas da União </md-tooltip>\n            </i>\n\n          </p>\n\n          <p style="padding: 0;">\n            <span style="font-weight: bold;">Cadastrador:</span>\n            {{item.noCadastrador}}\n          </p>\n          <p style="padding: 0;">\n            <span style="font-weight: bold;">Situação:</span>\n            {{item.situacao}}\n          </p>\n          <p style="padding: 0;">\n            <span style="font-weight: bold;">Data do Produto:</span>\n            {{item.dtCriacaoIdentificacao}}\n          </p>\n        </td>\n      </tr>\n      </tbody>\n    </table>\n  </md-table-container> --\x3e\n\n        <md-table-container ng-if="geoidentificacao.produtoCartRadio === true">\n            <table md-table md-row-select multiple="multiple" ng-model="produtosSelecionados" md-progress="buscaProdutosCartograficos" class="table-produtos-cartograficos">\n                <thead>\n                </thead>\n                <tbody ng-if="produtosCartograficos.data.length" md-body>\n                    <tr layout="row" layout-align="start center" md-select="produto" md-select-id="id" md-auto-select ng-repeat="produto in produtosCartograficos.data | filter:filtroPaginacaoProduto">\n                        <td layout="row" layout-align="center center">\n                            <md-checkbox class="md-primary" ng-model="produto.relacionado">\n                            </md-checkbox>\n                        </td>\n                        <td>\n                            <p>Título: {{ produto.titulo }}</p>\n                            <p>Nº do Processo da Área da União: {{ produto.numeroProcesso }} </p>\n                            <p>Cadastrador: {{ produto.nomeCadastrador }} </p>\n                            <p>Situação: {{ produto.situacao }} </p>\n                            <p>Data do Produto: {{ produto.data }} </p>\n                        </td>\n                    </tr>\n                </tbody>\n                <tbody md-body ng-if="!produtosCartograficos.data.length">\n                    <tr md-row class="center">\n                        <td colspan="6" style="text-align:center !important" md-cell>Nenhum resultado encontrado</td>\n                    </tr>\n                </tbody>\n            </table>\n        </md-table-container>\n\n        <md-table-pagination ng-if="geoidentificacao.produtoCartRadio === true" md-limit="produtosCartograficos.quantidade" md-limit-options="[5, 10, 15]" md-page="produtosCartograficos.pagina" md-total="{{produtosCartograficos.data.length}}" md-page-select></md-table-pagination>\n\n        <md-content layout-gt-sm="row" layout-padding>\n            <md-input-container style="width: 410px">\n                <label>Tipo de ação de demarcação/identificação</label>\n                <md-select ng-model="geoidentificacao.coTipoDemarcacao" name="coTipoDemarcacao" required ng-change="atualizarBotoesDoMapa();">\n                    <md-option ng-repeat="tipo in listTipoGeoidentificacao" ng-value="{{tipo.idTipoDemarcacao}}">{{ tipo.noTipoDemarcacao }}\n                    </md-option>\n                </md-select>\n                <div ng-messages="geoidentificacaoForm.coTipoDemarcacao.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n\n            <md-input-container style="width: 410px">\n                <label>Etapa</label>\n                <md-select ng-model="geoidentificacao.coEtapaDemarcacao" name="coEtapaDemarcacao" required ng-change="atualizarBotoesDoMapa();">\n                    <md-option title="{{etapa.dsHint}}" ng-repeat="etapa in listEtapaGeoidentificacao" ng-value="etapa.idEtapaDemarcacao">\n                        {{etapa.noEtapaDemarcacao }}\n                        <md-tooltip md-direction="top">Indica a etapa da demarcação de um trecho.</md-tooltip>\n                    </md-option>\n                </md-select>\n                <div ng-messages="geoidentificacaoForm.coEtapaDemarcacao.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n\n            <md-input-container style="width: 410px">\n                <label>Demanda Geradora do Processo</label>\n                <md-select ng-model="geoidentificacao.coFinalidadeDemarcacao" name="coFinalidadeDemarcacao" required>\n                    <md-option ng-repeat="finalidade in listFinalidadeGeoidentificacao" ng-value="{{ finalidade.idFinalidadeDemarcacao }}">\n                        {{ finalidade.noFinalidadeDemarcacao }}\n                        <md-tooltip md-direction="top">Indica a demanda que gerou o processo de demarcação, segundo as diretrizes do Art. 4º da Portaria Nº 140, de 14 de Maio de 2013.\n                        </md-tooltip>\n                    </md-option>\n                </md-select>\n                <div ng-messages="geoidentificacaoForm.coFinalidadeDemarcacao.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n        </md-content>\n\n\n        <div layout="row" layout-align="start start">\n            <div layout="column" layout-align="start start">\n                <md-content ng-repeat="portaria in geoidentificacao.portariaComissaoDemarcacao" nginit="opened=false" layout layout-align="start center">\n                    <md-input-container style="width:25em;">\n                        <label>Número da Portaria de Comissão de Demarcação</label>\n                        <input onkeypress="if(this.value.length == 10) return false" type="text" size="10" ng-model="portaria.nuPortariaComissaoDemarcacao">\n                    </md-input-container>\n\n                    <md-input-container style="width:35em;" layout>\n                        <md-button ng-click="opened = true" ng-blur="opened=false">\n                            <md-icon md-font-library="material-icons">date_range</md-icon>\n                        </md-button>\n                        <md-select ng-model="portaria.anoPortariaComissaoDemarcacao" opened="opened" manually-opened>\n                            <label> Ano da Portaria de Comissão de Demarcação </label>\n                            <md-option ng-repeat="ano in opcoesAnos" ng-value="ano">\n                                {{ ano }}\n                            </md-option>\n                        </md-select>\n                    </md-input-container>\n\n                    <md-button class="md-primary md-raised" ng-show="portaria.id != 0" ng-click="removePortaria(portaria)">\n                        <md-icon md-font-library="material-icons">delete</md-icon>\n                    </md-button>\n                </md-content>\n            </div>\n\n            <md-button class="md-primary md-raised" ng-click="addPortaria()" style="margin-top:30px;">\n                Adicionar Portaria\n            </md-button>\n        </div>\n\n\n        <md-content layout-gt-sm="row" layout-padding style="">\n            <label style="font-weight: bold;margin-right: -70px">Localização:</label>\n            <md-input-container style="margin-top:30px">\n                <md-checkbox ng-model="trochoIgualFim" hiny style="margin: 0" class="md-primary">\n                    Nome do trecho de inicio é igual ao de fim\n                </md-checkbox>\n                <md-tooltip md-direction="bottom">\n                    O trecho único é utilizado quando o trecho demarcado é delimitado por uma área específica (gleba, parque, município, etc.)\n                </md-tooltip>\n            </md-input-container>\n            <md-input-container ng-show="!trochoIgualFim" style="width: 410px;">\n                <label>Trecho Início</label>\n                <input type="text" ng-model="geoidentificacao.dsTrechoInicio" name="dsTrechoInicio">\n                <div ng-messages="geoidentificacaoForm.dsTrechoInicio.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n\n            <md-input-container ng-show="!trochoIgualFim" style="width: 410px">\n                <label>Trecho Fim</label>\n                <input type="text" ng-model="geoidentificacao.dsTrechoFim" name="dsTrechoFim">\n                <div ng-messages="geoidentificacaoForm.dsTrechoFim.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n\n            <md-input-container ng-show="trochoIgualFim" style="width: 820px">\n                <label>Trecho</label>\n                <input type="text" ng-model="geoidentificacao.dsTrecho" name="dsTrecho">\n                <div ng-messages="geoidentificacaoForm.dsTrecho.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n        </md-content>\n\n        <md-content>\n            <div style="display: flex">\n                <md-input-container style="width: 300px">\n                    <label>\n            <md-tooltip md-direction="top">Indica a situação atual do trecho de linha demarcatória.</md-tooltip>\n            Situação: {{geoidentificacao.dsSituacaoDemarcacao}}</label>\n                </md-input-container>\n\n                <md-input-container style="width: 876px; margin-left: 108px">\n                    <label>Observações</label>\n                    <textarea type="text" md-maxlength="850" ng-model="geoidentificacao.dsObservacao" name="dsObservacao"></textarea>\n                </md-input-container>\n            </div>\n        </md-content>\n\n\n        <md-content layout-gt-sm="row" layout-padding ng-show="geometria != null && geometriaValida">\n            <div>\n                <md-input-container style="width: 250px">\n                    <label>Área Aproximada(Km²) </label>\n                    <input disabled="disabled" ng-model="areaAproximadaEmKilometros" ui-number-mask ui-hide-group-sep>\n                </md-input-container>\n\n                <md-input-container style="width: 250px; margin-left: 25px">\n                    <label>Área Oficial(Km²)</label>\n                    <input ng-model="areaOficialEmKilometros" ng-blur="verificarDivergenciaDeArea()" ui-number-mask ui-hide-group-sep>\n                </md-input-container>\n            </div>\n        </md-content>\n\n        <div layout="row" layout-align="end center">\n            <md-button ng-show="showValidarGeometria" class="md-primary md-raised" ng-click="validarGeometria()">VALIDAR GEOMETRIA\n            </md-button>\n            <md-button class="md-primary md-raised" ng-click="cancelar()">CANCELAR</md-button>\n            <md-button class="md-primary md-raised" type="reset" ng-click="reset()">LIMPAR</md-button>\n            <md-button class="md-primary md-raised" type="submit">GRAVAR</md-button>\n\n        </div>\n    </form>\n</div>'),
t.put("scripts-geoidentificacao/geoidentificacao/geoidentificacao_consultar.html",'<h2 class="su-geoidentificacao-texto-principal">Pesquisar Geoidentificação de Áreas da União</h2>\n<div layout-align="center center" flex="100" layout-margin layout-wrap style="height: 500px; margin-bottom: 20px;">\n    <su-map map-id="map" mviewer="mviewer">\n        <div style="display: flex; position: relative;padding: 0.3em;" layout-align="center center" ng-if="legendaVisivel()">\n            <b>LEGENDA:</b>&emsp;\n            <div style="display: flex;" ng-if="controleLegenda.marinha">\n                <div class="retangulo marinha"></div>&ensp; Terreno de Marinha &emsp;\n            </div>\n            <div style="display: flex;" ng-if="controleLegenda.acrescidoMarinha">\n                <div class="retangulo acrescidoMarinha"></div>&ensp; Acrescido de Marinha&emsp;\n            </div>\n            <div style="display: flex;" ng-if="controleLegenda.marginal">\n                <div class="retangulo marginal"></div>&ensp; Terreno Marginal&emsp;\n            </div>\n            <div style="display: flex;" ng-if="controleLegenda.acrescidoMarginal">\n                <div class="retangulo acrescidoMarginal"></div>&ensp; Acrescido Marginal&emsp;\n            </div>\n            <div style="display: flex;" ng-if="controleLegenda.suspenso">\n                <div class="retangulo suspenso"></div>&ensp; Área suspensa&emsp;\n            </div>\n            <div style="display: flex;" ng-if="controleLegenda.sobreposta">\n                <div class="retangulo sobreposta"></div>&ensp; Área sobreposta&emsp;\n            </div>\n            <div style="display: flex;" ng-if="controleLegenda.invalido">\n                <div class="retangulo invalido"></div>&ensp; Área inválida &emsp;\n            </div>\n            <div style="display: flex;" ng-if="controleLegenda.outro">\n                <div class="retangulo outro"></div>&ensp; Outros &emsp;\n            </div>\n            <div style="display: flex;" ng-if="controleLegenda.naoDemarcado">\n                <div class="retangulo rachurada"></div>&ensp; Não Demarcado &emsp;\n            </div>\n        </div>\n    </su-map>\n</div>\n\n\n<div layout="row" layout-xs="column">\n    <md-input-container flex="33">\n        <label>Região:</label>\n        <md-select ng-model="query.regiaoUF" ng-change="buscarRegiao()" name="coRegiao">\n            <md-option value="">Todos</md-option>\n            <md-option ng-click="zoomRegiao(\'Env[-61.6333122219999 : -45.90691787, -24.0701349370001 : -7.34808755000004]\')" ng-value="5">Centro-Oeste\n            </md-option>\n            <md-option ng-click="zoomRegiao(\'Env[-48.7575454299998 : -32.3780363509998, -18.3507048889999 : -1.04999211500001]\')" ng-value="2">Nordeste\n            </md-option>\n            <md-option ng-click="zoomRegiao(\'Env[-73.991437778 : -45.696034374, -13.6932783330001 : 5.27178990699993]\')" ng-value="1">Norte\n            </md-option>\n            <md-option ng-click="zoomRegiao(\'Env[-53.1144859999999 : -28.840489363, -25.31227032 : -14.232144789]\')" ng-value="3">Sudeste\n            </md-option>\n            <md-option ng-click="zoomRegiao(\'Env[-57.6438019999999 : -48.0231773019999, -33.750969 : -22.5170339999999]\')" ng-value="4">Sul\n            </md-option>\n        </md-select>\n    </md-input-container>\n    <md-input-container flex="33">\n        <label>UF</label>\n        <md-select ng-model="query.geocodigoUf" ng-disabled="activatedConsUf" name="geocodigoUf" ng-change="buscarMunicipios();">\n            <md-option ng-value="">Todos</md-option>\n            <md-option ng-repeat="uf in ufs | orderBy: \'noAreaPoliticoAdministrativa\'" ng-value="{{ uf.geocodigoUf }}" ng-click="zoomUf(uf.geomUfExtent)" extent="{{ uf.geomUfExtent }}">\n                {{ uf.noAreaPoliticoAdministrativa }}\n            </md-option>\n        </md-select>\n    </md-input-container>\n    <md-progress-circular md-mode="indeterminate" ng-show="isLoadingConsUF" md-diameter="30"></md-progress-circular>\n    <md-input-container flex="33">\n        <label>Município</label>\n        <md-select ng-model="query.geocodigoMunicipio" ng-disabled="activatedConsMun" name="geocodigoMunicipioIdentificacaoCdg" ng-change="selecionarMunicipio()">\n            <md-option ng-value="" ng-click="zoom(extentUf)">Todos</md-option>\n            <md-option ng-value="{{municipio.geocodigoMunicipio}}" ng-repeat="municipio in municipios | orderBy: \'noAreaPoliticoAdministrativa\'" ng-click="zoom(municipio.geomMunicipioExntent)" extent="{{municipio.geomMunicipioExntent}}">\n                {{ municipio.noAreaPoliticoAdministrativa }}\n            </md-option>\n        </md-select>\n    </md-input-container>\n    <md-progress-circular md-mode="indeterminate" ng-show="isLoadingConsMun" md-diameter="30"></md-progress-circular>\n</div>\n\n<div layout="row">\n    <md-input-container flex="33">\n        <label>Nº do Processo de Identificação:</label>\n        <input ng-model="query.nuProcessoDemarcatorio" name="nuProcessoDemarcatorio">\n    </md-input-container>\n\n    <md-input-container flex="33">\n        <label>Tipo de ação de demarcação/identificação:</label>\n        <md-select ng-model="query.coTipoDemarcacao" name="coTipoDemarcacao">\n            <md-option ng-value="">Todos</md-option>\n            <md-option ng-repeat="tipo in listTipoGeoidentificacao" ng-value="{{ tipo.idTipoDemarcacao }}">\n                {{ tipo.noTipoDemarcacao }}\n            </md-option>\n        </md-select>\n    </md-input-container>\n\n    <md-input-container flex="33">\n        <label>Etapa:</label>\n        <md-select ng-model="query.coEtapaDemarcacao" name="coEtapaDemarcacao" ng-change="changeSelectedEtapa(geoidentificacao.coEtapaDemarcacao)">\n            <md-option ng-value="">Todos</md-option>\n            <md-option ng-repeat="etapa in listEtapaGeoidentificacao" ng-value="{{ etapa.idEtapaDemarcacao }}">\n                {{ etapa.noEtapaDemarcacao }}\n            </md-option>\n        </md-select>\n    </md-input-container>\n</div>\n\n<div layout="row">\n    <md-input-container flex="33">\n        <label>Demanda Geradora do Processo:</label>\n        <md-select ng-model="query.coFinalidadeDemarcacao" name="coFinalidadeDemarcacao">\n            <md-option ng-value="">Todos</md-option>\n            <md-option ng-repeat="finalidade in listFinalidadeGeoidentificacao" ng-value="{{ finalidade.idFinalidadeDemarcacao }}">{{ finalidade.noFinalidadeDemarcacao }}\n            </md-option>\n        </md-select>\n    </md-input-container>\n    <md-input-container flex="33">\n        <label>Situação</label>\n        <md-select ng-model="query.icSituacaoDemarcacao" name="icSituacaoDemarcacao">\n            <md-option ng-value="">Todos</md-option>\n            <md-option ng-value="true">Demarcada</md-option>\n            <md-option ng-value="false">Não Demarcada</md-option>\n        </md-select>\n    </md-input-container>\n    <md-input-container flex="33">\n        <label>Cadastrador</label>\n        <md-select ng-model="query.coCadastrador" ng-change="" name="coCadastrador">\n            <md-option value="">Todos</md-option>\n            <md-option ng-repeat="cad in cadastradores | orderBy: \'nome\'" ng-value="\'{{ cad.cpf }}\'">{{ cad.nome }}</md-option>\n        </md-select>\n    </md-input-container>\n</div>\n\n<h4>Data de cadastro:</h4>\n<div layout="row" layout-xs="column" layout-margin>\n    <div flex-gt-xs="33">\n        <div style="margin-left: 64px;">Inicial</div>\n        <md-datepicker id="dtCadastroIni" ng-model="query.dtInicial" md-date-filter="onlyWeekendsPredicate" md-max-date="maxDate" name="dtInicial" md-open-on-focus ng-blur="formataDtIni()"></md-datepicker>\n    </div>\n    <div flex-gt-xs="33">\n        <div style="margin-left: 64px;">Final</div>\n        <md-datepicker id="dtCadastroFim" ng-model="query.dtFinal" md-date-filter="onlyWeekendsPredicate" md-max-date="maxDate" name="dtFinal" md-open-on-focus ng-blur="formataDtFim()"></md-datepicker>\n    </div>\n</div>\n<div layout="row" layout-align="end center">\n    <md-button class="md-primary md-raised" type="reset" ng-click="limparForm()">LIMPAR</md-button>\n    <md-button class="md-primary md-raised" id="btPesquisar" type="button" ng-click="filtrar()">PESQUISAR</md-button>\n    <md-button ng-show="authenticatedCadastrar == true" class="md-primary md-raised" type="button" ui-sref="GEOIDENTIFICACAO.geoidentificacaocadastrar">NOVO\n    </md-button>\n    <md-button ng-show="authenticatedCadastrar == false" class="md-primary md-raised" type="button" ng-click="alertPermissao(\'cadastrar\');">NOVO\n    </md-button>\n</div>\n<form name="dataTabelaGeoIdentificacao" ng-if="geoidentificacoes.length > 0">\n\n    <md-table-container layout="column">\n        <table md-table class="tb-acrescido">\n            <thead class="md-head">\n                <th md-cell>\n                    Detalhes\n                </th>\n                <th md-cell>\n                    <div class="center">\n                        Produto de CDG Associado\n                    </div>\n                </th>\n                <th md-cell>\n                    Ações\n                </th>\n            </thead>\n            <tbody md-body ng-repeat="item in geoidentificacoes">\n                <tr md-row>\n                    <td md-cell class="shine" style="max-width: 500px">\n                        <div class="width-100 block">\n                            <div class="su-geoidentificacao-flex">\n                                <div class="width-50 right"><b>Nº do Processo de Identificação:&nbsp;</b></div>\n                                <div class="width-50 left">\n                                    {{item.nuProcessoDemarcatorio ? item.nuProcessoDemarcatorio : \'Este produto cartográfico não está vinculado a nenhuma Geoidentificação de Áreas da União\'}}\n                                </div>\n                            </div>\n                            <div class="su-geoidentificacao-flex">\n                                <div class="width-50 right"><b>Etapa:&nbsp;</b></div>\n                                <div class="width-50 left">{{item.tipoDemarcacao.noTipoDemarcacao}}</div>\n                            </div>\n                            <div class="su-geoidentificacao-flex">\n                                <div class="width-50 right"><b>Tipo de ação de demarcação/identificação:&nbsp;</b></div>\n                                <div class="width-50 left">{{item.etapaDemarcacao.noEtapaDemarcacao}}</div>\n                            </div>\n                            <div class="su-geoidentificacao-flex">\n                                <div class="width-50 right"><b>Demanda Geradora do Processo:&nbsp;</b></div>\n                                <div class="width-50 left">{{item.finalidadeDemarcacao.noFinalidadeDemarcacao}}</div>\n                            </div>\n                            <div class="su-geoidentificacao-flex">\n                                <div class="width-50 right"><b>Cadastrador:&nbsp;</b></div>\n                                <div class="width-50 left">{{item.noCadastrador}}</div>\n                            </div>\n                            <div class="su-geoidentificacao-flex">\n                                <div class="width-50 right"><b>Região:&nbsp;</b></div>\n                                <div class="width-50 left">{{item.nomeRegiao}}</div>\n                            </div>\n                            <div class="su-geoidentificacao-flex">\n                                <div class="width-50 right"><b>UF:&nbsp;</b></div>\n                                <div class="width-50 left">{{item.nomeUF}}</div>\n                            </div>\n                        </div>\n                    </td>\n                    <td md-cell>\n                        <div class="center">\n                            <b>{{item.coProdutosCartograficosRelacionados.length}}</b>\n                        </div>\n                    </td>\n                    <td md-cell>\n                        <div class="center">\n\n                            <md-fab-speed-dial md-direction="left" class="hand md-scale ng-isolate-scope _md-animations-ready md-left" ng-class="{ \'alinhar-md-fab-speed-dial\': destinacao.tipoDestinacao.id !== 12 &amp;&amp;  destinacao.tipoDestinacao.id !== 13 }">\n                                <div class="_md-css-variables"></div>\n                                <md-fab-trigger aria-haspopup="true" aria-expanded="false">\n                                    <ng-md-icon class="icon-acao" icon="more_vert">\n                                    </ng-md-icon>\n                                </md-fab-trigger>\n                                <md-fab-actions aria-hidden="true">\n                                    <div class="md-fab-action-item">\n                                        <ng-md-icon class="icon-subacao" alt="Histórico" icon="access_time" ui-sref="GEOIDENTIFICACAO.historico({id: item.idProcessoDemarcatorio})">\n                                            <md-tooltip>Históricos</md-tooltip>\n                                        </ng-md-icon>\n                                    </div>\n                                    <div class="md-fab-action-item">\n                                        <ng-md-icon ng-show="authenticatedExcluir == true" class="icon-subacao" alt="Visualizar" icon="delete" ng-click="excluir(item.idProcessoDemarcatorio)">\n                                            <md-tooltip>Excluir</md-tooltip>\n                                        </ng-md-icon>\n                                    </div>\n                                    <div class="md-fab-action-item">\n                                        <ng-md-icon ng-show="authenticatedExcluir == false" class="icon-subacao" alt="Visualizar" icon="delete" ng-click="alertPermissao(\'excluir\');" style="color:#D3D3D3">\n                                            <md-tooltip>Excluir</md-tooltip>\n                                        </ng-md-icon>\n                                    </div>\n\n                                    <div class="md-fab-action-item">\n                                        <ng-md-icon ng-show="authenticatedEditar == true" class="icon-subacao" alt="Visualizar" icon="create" ui-sref="GEOIDENTIFICACAO.geoidentificacaoeditar({id: item.idProcessoDemarcatorio})">\n                                            <md-tooltip>Editar</md-tooltip>\n                                        </ng-md-icon>\n                                    </div>\n                                    <div class="md-fab-action-item">\n                                        <ng-md-icon ng-show="authenticatedEditar == false" class="icon-subacao" alt="Visualizar" icon="create" ng-click="alertPermissao(\'editar\');" style="color:#D3D3D3">\n                                            <md-tooltip>Editar</md-tooltip>\n                                        </ng-md-icon>\n                                    </div>\n\n                                    <div class="md-fab-action-item">\n                                        <ng-md-icon ng-click="verNoMapa(item);" class="icon-subacao" alt="Ver no mapa" icon="map">\n                                            <md-tooltip>Ver no mapa</md-tooltip>\n                                        </ng-md-icon>\n                                    </div>\n\n                                    <div class="md-fab-action-item">\n                                        <ng-md-icon class="icon-subacao" alt="Visualizar" icon="search" ng-click="visualizar(item.idProcessoDemarcatorio)">\n                                            <md-tooltip>Visualizar</md-tooltip>\n                                        </ng-md-icon>\n                                    </div>\n                                </md-fab-actions>\n                            </md-fab-speed-dial>\n\n                        </div>\n                    </td>\n                </tr>\n            </tbody>\n        </table>\n    </md-table-container>\n\n    <md-table-pagination md-limit="query.size" md-limit-options="[5, 10, 15]" md-page="query.page" md-total="{{total}}" md-on-paginate="filtrar" md-page-select></md-table-pagination>\n</form>\n\n<div id="info-popup" class="ol-popup" ng-click="closeInfo()" style="display: none">\n    <a href="#" id="popup-closer" class="ol-popup-closer"></a>\n    <div id="popup-content"></div>\n</div>'),
t.put("scripts-geoidentificacao/geoidentificacao/geoidentificacao_editar.html",'<div ng-show="authenticatedEditar">\n    <form name="geoidentificacaoForm" ng-submit="submit()" novalidate>\n        <h2 class="su-geoidentificacao-texto-principal">Editar Geoidentificação de Áreas da União</h2>\n        <div layout-align="center center" layout-margin layout-wrap style="min-height: 500px; margin-bottom: 20px;">\n            <gi-map ng-if="geometria" on-init-map="$ctrl.mapApi = $API;" mviewer="mviewer" geometry="geometria" mostrar-botao-upload="mostrarBotaoUpload" mostrar-botao-desenhar="mostrarBotaoDesenhar" class="mapEditar">\n                <div style="display: flex; position: relative;" layout-align="center center" ng-if="legendaVisivel()">\n                    <b>LEGENDA:</b>&emsp;\n                    <div style="display: flex;" ng-if="controleLegenda.marinha">\n                        <div class="retangulo marinha"></div>&ensp; Terreno de Marinha &emsp;\n                    </div>\n                    <div style="display: flex;" ng-if="controleLegenda.acrescidoMarinha">\n                        <div class="retangulo acrescidoMarinha"></div>&ensp; Acrescido de Marinha&emsp;\n                    </div>\n                    <div style="display: flex;" ng-if="controleLegenda.marginal">\n                        <div class="retangulo marginal"></div>&ensp; Terreno Marginal&emsp;\n                    </div>\n                    <div style="display: flex;" ng-if="controleLegenda.acrescidoMarginal">\n                        <div class="retangulo acrescidoMarginal"></div>&ensp; Acrescido Marginal&emsp;\n                    </div>\n                    <div style="display: flex;" ng-if="controleLegenda.suspenso">\n                        <div class="retangulo suspenso"></div>&ensp; Área suspensa&emsp;\n                    </div>\n                    <div style="display: flex;" ng-if="controleLegenda.sobreposta || geoidentificacao.coProdutosCartograficosRelacionados.length">\n                        <div class="retangulo sobreposta"></div>&ensp; Área sobreposta&emsp;\n                    </div>\n                    <div style="display: flex;" ng-if="controleLegenda.invalido">\n                        <div class="retangulo invalido"></div>&ensp; Área inválida &emsp;\n                    </div>\n                    <div style="display: flex;" ng-if="controleLegenda.outro || geoidentificacao.coProdutosCartograficosRelacionados.length">\n                        <div class="retangulo outro"></div>&ensp; Outros &emsp;\n                    </div>\n                    <div style="display: flex;" ng-if="controleLegenda.naoDemarcado">\n                        <div class="retangulo rachurada"></div>&ensp; Não Demarcado &emsp;\n                    </div>\n                </div>\n            </gi-map>\n            <br>\n            <div layout="row" layout-margin class="identificacao" layout-xs="column" ng-show="showCheckVisualizarProdCartGeomEditar">\n                <md-checkbox ng-model="produtoCartVisualizarEditar" ng-change="visibilidadeGeomProdCartEditar(produtoCartVisualizarEditar)"><span name="labelVisualizarProdCart">Visualizar Produtos Cartográficos</span></md-checkbox>\n            </div>\n        </div>\n\n        <div layout="column">\n            <div layout="row" style="margin-top: -220px">\n                <md-content layout-gt-sm="row" layout-padding>\n                    <md-input-container>\n                        <label>Nº do Processo de identificação</label>\n                        <input ng-model="geoidentificacao.nuProcessoDemarcatorio" name="nuProcessoDemarcatorio" size="50" ng-blur="consultaDocumentoSei()" onkeypress="if(this.value.length == 254) return false" md-maxlength="254">\n                        <div ng-messages="geoidentificacaoForm.nuProcessoDemarcatorio.$error">\n                            <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                        </div>\n                    </md-input-container>\n                </md-content>\n            </div>\n            <div layout="row">\n                <md-content layout-gt-sm="row" layout-padding>\n                    <md-input-container style="width: 425px">\n                        <label>Nível de Precisão Cartográfica</label>\n                        <md-select name="nivelPrecisaoCartografica" ng-model="geoidentificacao.niveisPrecisaoCartografica" ng-disabled="geoidentificacao.niveisPrecisaoCartografica.idNiveisPrecisaoCartografica === 2" ng-model-options="{trackBy: \'$value.idNiveisPrecisaoCartografica\' }">\n                            <md-option ng-repeat="niveil in niveisPrecisaoCartografica" ng-value="niveil">\n                                {{niveil.dsNiveisPrecisaoCartografica}}\n                                <md-tooltip md-direction="top">Indica o nível de precisão de uma feição obtida a partir de um Produto Cartográfico.</md-tooltip>\n                            </md-option>\n                        </md-select>\n                    </md-input-container>\n                </md-content>\n            </div>\n        </div>\n\n        <div class="mg-bottom-20">\n            <div ng-if="showProdCartEmAreaUniao">\n                <label>Essa Geoidentificação de Áreas da União possui produtos cartográficos associados a ela?</label>\n\n                <md-content>\n                    <div>\n                        <md-input-container>\n                            <md-radio-group layout="row" ng-model="geoidentificacao.produtoCartRadio" ng-change="buscarProdutos(geoidentificacao.produtoCartRadio, false)">\n                                <md-radio-button style="width: 93px" value="true" class="md-primary">Sim</md-radio-button>\n                                <md-radio-button value="false" class="md-primary"> Não </md-radio-button>\n                            </md-radio-group>\n                        </md-input-container>\n\n                        <md-input-container style="width: 650px; margin-left: 250px" ng-if="geoidentificacao.produtoCartRadio === \'false\'">\n                            <label>Justificativa</label>\n                            <input ng-model="geoidentificacao.dsJustificativaGeometadados" name="dsJustificativaGeometadados" md-maxlength="200" required>\n                            <div ng-messages="geoidentificacaoForm.dsJustificativaGeometadados.$error">\n                                <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                            </div>\n                        </md-input-container>\n                    </div>\n                </md-content>\n            </div>\n\n            <div ng-if="geoidentificacao.produtoCartRadio === \'true\'">\n                <md-table-container>\n                    <table md-table md-row-select multiple="multiple" ng-model="produtosSelecionados" md-progress="buscaProdutosCartograficos" class="table-produtos-cartograficos">\n                        <thead>\n                        </thead>\n                        <tbody md-body>\n                            <tr layout="row" ng-if="produtosCartograficos.data.length" layout-align="start center" ng-repeat="produto in produtosCartograficos.data">\n                                <td layout="row" layout-align="center center">\n                                    <md-checkbox ng-change="selecionarProdutoCartografico(produto.id, isProdutosCartograficos[$index])" class="md-primary" ng-model="isProdutosCartograficos[$index]" aria-label="Selecionar produto cartográfico">\n                                    </md-checkbox>\n                                </td>\n                                <td>\n                                    <p>Título: {{ produto.titulo }}</p>\n                                    <p>Nº do Processo da Área da União: {{ produto.numeroProcesso }} </p>\n                                    <p>Cadastrador: {{ produto.nomeCadastrador }} </p>\n                                    <p>Situação: {{ produto.situacao }} </p>\n                                    <p>Data do Produto: {{ produto.data }} </p>\n                                </td>\n                            </tr>\n                        </tbody>\n                        <tbody md-body ng-if="!produtosCartograficos.data.length">\n                            <tr md-row class="center">\n                                <td style="text-align:center !important" colspan="6" md-cell>Nenhum resultado encontrado</td>\n                            </tr>\n                        </tbody>\n                    </table>\n                </md-table-container>\n\n                <md-table-pagination ng-if="geoidentificacao.produtoCartRadio" md-limit="produtosCartograficos.quantidade" md-limit-options="[5, 10, 15]" md-page="produtosCartograficos.pagina" md-total="{{produtosCartograficos.data.length}}" md-page-select></md-table-pagination>\n            </div>\n\n        </div>\n\n        <div layout="row" layout-xs="column" layout-padding>\n            <md-input-container flex="33">\n                <label>Tipo de ação de demarcação/identificação:</label>\n                <md-select ng-model="geoidentificacao.coTipoDemarcacao" name="coTipoDemarcacao" required>\n                    <md-option ng-repeat="tipo in listTipoGeoidentificacao" ng-value="{{ tipo.idTipoDemarcacao }}">{{ tipo.noTipoDemarcacao }}</md-option>\n                </md-select>\n                <div ng-messages="geoidentificacaoForm.coTipoDemarcacao.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n\n            <md-input-container flex="33">\n                <label>Etapa:</label>\n                <md-select ng-model="geoidentificacao.coEtapaDemarcacao" name="coEtapaDemarcacao" ng-change="atualizarBotoesDoMapa();changeSelectedEtapa(geoidentificacao.coEtapaDemarcacao)" required>\n                    <md-option ng-repeat="etapa in listEtapaGeoidentificacao" ng-value="{{ etapa.idEtapaDemarcacao }}">{{ etapa.noEtapaDemarcacao }}</md-option>\n                </md-select>\n                <div ng-messages="geoidentificacaoForm.coEtapaDemarcacao.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n\n            <md-input-container flex="33">\n                <label>Demanda geradora do processo:</label>\n                <md-select ng-model="geoidentificacao.coFinalidadeDemarcacao" name="coFinalidadeDemarcacao" required>\n                    <md-option ng-repeat="finalidade in listFinalidadeGeoidentificacao" ng-value="{{ finalidade.idFinalidadeDemarcacao }}">{{ finalidade.noFinalidadeDemarcacao }}</md-option>\n                </md-select>\n                <div ng-messages="geoidentificacaoForm.coFinalidadeDemarcacao.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n        </div>\n\n        <md-button class="md-primary md-raised" style="float: right;margin-top: 45px;margin-right: 300px" ng-click="addPortaria()">Adicionar Portaria\n        </md-button>\n\n        <md-content ng-repeat="portaria in geoidentificacao.portariaComissaoDemarcacao" layout-gt-sm="row" layout-padding>\n            <div>\n                <md-input-container style="width: 450px">\n                    <label>Número da Portaria de Comissão de Demarcação</label>\n                    <input type="number" ng-blur="vericarPortariaAlterada(portaria)" ng-model="portaria.nuPortariaComissaoDemarcacao">\n                </md-input-container>\n\n                <md-input-container>\n                    <p> Ano da Portaria de Comissão de Demarcação</p>\n                    <md-datepicker name="anoPortariaComissaoDemarcacao" md-max-date="maxDate" ng-model="portaria.anoPortariaComissaoDemarcacao" md-date-locale="monthFormat" md-placeholder="Ano" md-mode="month" md-open-on-focus="true">\n                    </md-datepicker>\n                </md-input-container>\n                <md-button class="md-primary md-raised" ng-show="$index > 0" style="margin-left: 70px" ng-click="removePortaria(portaria)">\n                    <md-icon ng-blur="vericarPortariaAlterada(portaria)" md-font-library="material-icons">delete</md-icon>\n                </md-button>\n            </div>\n        </md-content>\n\n        <md-content layout-gt-sm="row" layout-padding>\n            <label style="font-weight: bold;margin-right: -70px">Localização:</label>\n            <md-input-container style="margin-top:30px">\n                <md-checkbox ng-disabled="isPortariaAlterada" ng-model="trochoIgualFim" hiny style="margin: 0" class="md-primary" ng-change="changeTrechoIgualFim()" aria-label="Trecho igual ao fim">\n                    Nome do trecho de inicio é igual ao de fim\n                </md-checkbox>\n                <md-tooltip md-direction="bottom">\n                    O trecho único é utilizado quando o trecho demarcado é delimitado por uma área específica (gleba, parque, município, etc.)\n                </md-tooltip>\n            </md-input-container>\n            <md-input-container ng-show="!trochoIgualFim" style="width: 410px;">\n                <label>Trecho Início</label>\n                <input type="text" ng-disabled="isPortariaAlterada" ng-model="geoidentificacao.dsTrechoInicio" name="dsTrechoInicio">\n                <div ng-messages="geoidentificacaoForm.dsTrechoInicio.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n\n            <md-input-container ng-show="!trochoIgualFim" style="width: 410px">\n                <label>Trecho Fim</label>\n                <input type="text" ng-disabled="isPortariaAlterada" ng-model="geoidentificacao.dsTrechoFim" name="dsTrechoFim">\n                <div ng-messages="geoidentificacaoForm.dsTrechoFim.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n\n            <md-input-container ng-show="trochoIgualFim" style="width: 820px">\n                <label>Trecho</label>\n                <input type="text" ng-disabled="isPortariaAlterada" ng-model="geoidentificacao.dsTrecho" name="dsTrecho">\n                <div ng-messages="geoidentificacaoForm.dsTrecho.$error">\n                    <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                </div>\n            </md-input-container>\n        </md-content>\n\n        <div layout="row" layout-xs="column">\n            <md-input-container flex="30" flex-xs="100">\n                <label>\n                    <md-tooltip md-direction="top">Indica a situação atual do trecho de linha demarcatória.</md-tooltip>\n                    Situação: {{geoidentificacao.dsSituacaoDemarcacao}}</label>\n            </md-input-container>\n\n            <md-input-container flex="70" flex-xs="100">\n                <label>Observações:</label>\n                <textarea type="text" md-maxlength="850" ng-model="geoidentificacao.dsObservacao" name="dsObservacao"></textarea>\n            </md-input-container>\n        </div>\n\n        <div layout="row" layout-xs="column">\n            <md-input-container flex="50" flex-xs="100">\n                <label>Área Aproximada(KM²)</label>\n                <input ui-number-mask ui-hide-group-sep disabled="disabled" ng-model="areaAproximadaEmKilometros">\n            </md-input-container>\n\n            <md-input-container flex="50" flex-xs="100">\n                <label>Área Oficial(KM²)</label>\n                <input ui-number-mask ui-hide-group-sep ng-model="areaOficialEmKilometros" ng-blur="verificarDivergenciaDeArea()">\n            </md-input-container>\n        </div>\n\n        <md-card>\n            <md-card-header>\n                <h2 class="texto-principal width-100" style="width: 100%;">Terreno Acrescido {{geoidentificacao.coTipoDemarcacao === 1 ? \'de Marinha\' : \'Marginal\'}}</h2>\n            </md-card-header>\n            <md-card-content>\n                <div ng-if="!arquivosAcrescidos || !arquivosAcrescidos.content.length">\n                    <div ng-cloack layout="row" layout-align="start center">\n                        <md-button flex-xs="100" ngf-select="uploadFile($file)" class="md-primary md-raised" aria-label="Incluir Arquivo">Importar Geometria\n                            <md-tooltip md-direction="bottom" style="width: 300px;height:120px">\n                                O arquivo shapefile deve estar compactado em um arquivo .zip. O<br> arquivo .zip deve conter pelo menos os componentes dos arquivos<br> .shp, .shx, dbf e .prj do shapefile. O arquivo shapefile deve ser<br> apresentado\n                                com DATUM SIRGAS 2000 em coordenadas<br> geograficas (Código EPSG: 4674).\n                            </md-tooltip>\n                        </md-button>\n                        <md-input-container flex="33" flex-xs="100">\n                            <label>Área Aproximada(KM²)</label>\n                            <input ui-number-mask ui-hide-group-sep disabled="disabled" ng-model="terrenoAcrescido.areaAproximada">\n                        </md-input-container>\n\n                        <md-input-container flex="33" flex-xs="100">\n                            <label>Área Oficial(KM²)*</label>\n                            <input ui-number-mask ui-hide-group-sep ng-model="terrenoAcrescido.areaOficial" ng-blur="verificarDivergenciaDeAreaAcrescida()">\n                        </md-input-container>\n                    </div>\n\n                    <div ng-cloack layout="row" layout-align="end center">\n                        <md-button ng-click="adicionarTerrenoAcrescido()" class="md-primary md-raised" aria-label="Adicionar Terreno Acrescido">\n                            {{(terrenoAcrescido && terrenoAcrescido.id) ? \'Alterar Terreno Acrescido\' : \'Adicionar Terreno Acrescido\'}}\n                        </md-button>\n                    </div>\n                </div>\n                <div ng-if="arquivosAcrescidos && arquivosAcrescidos.content.length">\n                    <md-table-container>\n                        <table md-table class="tb-acrescido">\n                            <thead class="md-head">\n                                <th md-cell>\n                                    Arquivo da Geometria\n                                </th>\n                                <th md-cell>\n                                    Área aproximada (km²)\n                                </th>\n                                <th md-cell>\n                                    Área Oficial (km²)\n                                </th>\n                                <th md-cell>\n                                    Ações\n                                </th>\n                            </thead>\n                            <tbody md-body ng-repeat="item in arquivosAcrescidos.content">\n                                <tr md-row>\n                                    <td md-cell>\n                                        <button type="button" class="btn-download" ng-click="download(item)">{{item.nomeArquivo}}</button></td>\n                                    \n                                    <td md-cell>\n                                        {{item.areaAproximada}}\n                                    </td>\n                                    <td md-cell>\n                                        {{item.areaOficial}}\n                                    </td>\n                                    <td md-cell width="150px;">\n                                        <md-button ng-click="editarTerrenoAcrescido(item, $index)" class="md-fab md-raised md-mini" aria-label="Editar Terreno Acrescido">\n                                            <md-icon ng-bind="\'edit\'">\n                                            </md-icon>\n                                        </md-button>\n                                        <md-button ng-click="excluirTerrenoAcrescido(item.id, $index)" class="md-fab md-raised md-mini" aria-label="Excluir Terreno Acrescido">\n                                            <md-icon ng-bind="\'delete\'">\n                                            </md-icon>\n                                        </md-button>\n                                    </td>\n                                </tr>\n                            </tbody>\n                        </table>\n                    </md-table-container>\n\n                    \x3c!-- <md-table-pagination md-limit="paginacaoArquivoAcrescido.size" md-limit-options="[5, 10, 15]" md-page="paginacaoArquivoAcrescido.page" md-total="{{arquivosAcrescidos.totalElements}}" md-on-paginate="filtrarArquivoAcrescido" md-page-select></md-table-pagination> --\x3e\n\n                </div>\n\n                <md-content layout-gt-sm="row" layout-padding ng-if="geoidentificacao.existeTerrenoAcrescidoRemovido">\n                    <md-input-container style="margin-top:30px">\n                        <md-checkbox class="md-primary" ng-click="toggleVerTerrenosRemovidos()">\n                            Ver terrenos acrescidos removidos\n                        </md-checkbox>\n                    </md-input-container>\n                </md-content>\n\n                <div>\n                    <md-table-container ng-if="verTerrenosRemovidos">\n                        <table md-table class="tb-acrescido">\n                            <thead class="md-head">\n                                <th md-cell>\n                                    Arquivo da Geometria\n                                </th>\n                                <th md-cell>\n                                    Área aproximada (km²)\n                                </th>\n                                <th md-cell>\n                                    Área Oficial (km²)\n                                </th>\n                            </thead>\n                            <tbody md-body ng-repeat="item in arquivosRemovido.content">\n                                <tr md-row>\n                                    <td md-cell>\n                                        <button type="button" class="btn-download" ng-click="download(item)">{{item.nomeArquivo}}</button></td>\n                                    \n                                    <td md-cell>\n                                        {{item.areaAproximada}}\n                                    </td>\n                                    <td md-cell>\n                                        {{item.areaOficial}}\n                                    </td>\n                                </tr>\n                            </tbody>\n                        </table>\n                    </md-table-container>\n\n                    <md-table-pagination ng-if="verTerrenosRemovidos" md-limit="paginacaoArquivoAcrescidoRemovidos.size" md-limit-options="[5, 10, 15]" md-page="paginacaoArquivoAcrescidoRemovidos.page" md-total="{{arquivosRemovido.totalElements}}" md-on-paginate="filtrarArquivoAcrescidoRemovido" md-page-select></md-table-pagination>\n\n                </div>\n            </md-card-content>\n        </md-card>\n\n        <md-card>\n            <md-card-header>\n                <h2 class="texto-principal width-100" style="width: 100%;">Suspensões</h2>\n            </md-card-header>\n            <md-card-content>\n                <div ng-cloack layout="row" layout-align="end center">\n                    <md-button ng-disable="!geoidentificacao" ng-click="adicionarSuspensao($event, undefined, false)" class="md-primary md-raised" aria-label="Adicionar Suspensão">\n                        Adicionar Suspensão\n                    </md-button>\n                </div>\n\n                <md-table-container layout="column">\n                    <table md-table class="tb-acrescido">\n                        <thead class="md-head">\n                            <th md-cell>\n                                Tipo\n                            </th>\n                            <th md-cell>\n                                Área Oficial (km²)\n                            </th>\n                            <th md-cell>\n                                Início da Supensão\n                            </th>\n                            <th md-cell>\n                                Fim da Suspensão\n                            </th>\n                            <th md-cell>\n                                Situação\n                            </th>\n                            <th md-cell>\n                                Ações\n                            </th>\n                        </thead>\n                        <tbody md-body ng-if="geoidentificacaoSuspensaoPage.content.length" ng-repeat="item in geoidentificacaoSuspensaoPage.content">\n                            <tr md-row>\n                                <td md-cell>\n                                    {{item.tipoSuspensao.noTipoSuspensao}}\n                                </td>\n                                <td md-cell>\n                                    {{item.areaOficial}}\n                                </td>\n                                <td md-cell>\n                                    {{item.dtInicio | date: \'dd/MM/yyyy\'}}\n                                </td>\n                                <td md-cell>\n                                    {{item.dtFim | date: \'dd/MM/yyyy\'}}\n                                </td>\n                                <td md-cell>\n                                    {{item.isAtivo ? \'Ativo\' : \'Encerrado\'}}\n                                </td>\n                                <td md-cell width="200px">\n                                    <md-button ng-click="verAreaSuspensa(item)" class="md-fab md-raised md-mini" aria-label="Ver no mapa">\n                                        <md-icon ng-bind="\'map\'">\n                                        </md-icon>\n                                    </md-button>\n                                    <md-button ng-click="adicionarSuspensao($event, item.id, false)" class="md-fab md-raised md-mini" aria-label="Excluir Terreno Acrescido">\n                                        <md-icon ng-bind="\'edit\'">\n                                        </md-icon>\n                                    </md-button>\n                                    <md-button ng-click="removerSuspensao(item.id, $index)" class="md-fab md-raised md-mini" aria-label="Excluir Terreno Acrescido">\n                                        <md-icon ng-bind="\'delete\'">\n                                        </md-icon>\n                                    </md-button>\n                                </td>\n                            </tr>\n                        </tbody>\n                        <tbody md-body ng-if="!geoidentificacaoSuspensaoPage.content.length">\n                            <tr md-row class="center">\n                                <td colspan="6" style="text-align:center !important" md-cell>Nenhum resultado encontrado</td>\n                            </tr>\n                        </tbody>\n                    </table>\n                </md-table-container>\n\n                <md-table-pagination md-limit="paginacaoSuspensao.size" md-limit-options="[5, 10, 15]" md-page="paginacaoSuspensao.page" md-total="{{geoidentificacaoSuspensaoPage.totalElements}}" md-on-paginate="filtrarSuspensao" md-page-select></md-table-pagination>\n\n                <md-content layout-gt-sm="row" layout-padding ng-if="geoidentificacao.existeSuspensaoRemovida">\n                    <md-input-container style="margin-top:30px">\n                        <md-checkbox class="md-primary" ng-click="toggleVerSuspensoesRemovidas()">\n                            Ver suspensões removidos\n                        </md-checkbox>\n                    </md-input-container>\n                </md-content>\n\n                <md-table-container ng-if="isSuspensoesRemovidas" layout="column">\n                    <table md-table class="tb-acrescido">\n                        <thead class="md-head">\n                            <th md-cell>\n                                Tipo\n                            </th>\n                            <th md-cell>\n                                Área Oficial (km²)\n                            </th>\n                            <th md-cell>\n                                Início da Supensão\n                            </th>\n                            <th md-cell>\n                                Fim da Suspensão\n                            </th>\n                            <th md-cell>\n                                Situação\n                            </th>\n                            <th md-cell>\n                                Ações\n                            </th>\n                        </thead>\n                        <tbody md-body ng-repeat="item in geoidentificacaoSuspensaoRemovidaPage.content">\n                            <tr md-row>\n                                <td md-cell>\n                                    {{item.tipoSuspensao.noTipoSuspensao}}\n                                </td>\n                                <td md-cell>\n                                    {{item.areaOficial}}\n                                </td>\n                                <td md-cell>\n                                    {{item.dtInicio | date: \'dd/MM/yyyy\'}}\n                                </td>\n                                <td md-cell>\n                                    {{item.dtFim | date: \'dd/MM/yyyy\'}}\n                                </td>\n                                <td md-cell>\n                                    {{item.isAtivo ? \'Ativo\' : \'Encerrado\'}}\n                                </td>\n                                <td md-cell width="50px">\n                                    <md-button ng-click="adicionarSuspensao($event, item.id, true)" class="md-fab md-raised md-mini" aria-label="Visualizar suspensão">\n                                        <md-icon ng-bind="\'search\'">\n                                        </md-icon>\n                                    </md-button>\n                                </td>\n                            </tr>\n                        </tbody>\n                    </table>\n                </md-table-container>\n\n                <md-table-pagination ng-if="isSuspensoesRemovidas" md-limit="paginacaoSuspensaoRemovida.size" md-limit-options="[5, 10, 15]" md-page="paginacaoSuspensaoRemovida.page" md-total="{{geoidentificacaoSuspensaoRemovidaPage.totalElements}}" md-on-paginate="filtrarSuspensaoRemovida" md-page-select></md-table-pagination>\n\n            </md-card-content>\n        </md-card>\n\n        <div layout="row" layout-align="end center">\n            <md-button class="md-primary md-raised" ng-click="cancelar()">CANCELAR</md-button>\n            <md-button class="md-primary md-raised" type="reset" ng-click="reset()">LIMPAR</md-button>\n            <md-button class="md-primary md-raised" type="submit">GRAVAR</md-button>\n        </div>\n\n    </form>\n</div>'),
t.put("scripts-geoidentificacao/geoidentificacao/geoidentificacao_historico.html",'<style>\n    table {\n        width: 100%;\n    }\n    \n    td.td-bold {\n        font-weight: bold;\n        text-align: end;\n        width: 30%;\n    }\n</style>\n\n<h2 class="su-geoidentificacao-texto-principal">Histórico Geoidentificação</h2>\n<md-table-container>\n    <table>\n        <tbody>\n            <tr>\n                <td class="td-bold">N° do processo da Área da União:</td>\n                <td><label>{{geoidentificacao.nuProcessoDemarcatorio}}</label></td>\n            </tr>\n            <tr>\n                <td class="td-bold">Cadastrador:</td>\n                <td><label>{{geoidentificacao.noCadastrador}}</label></td>\n            </tr>\n            <tr>\n                <td class="td-bold">Tipo de Ação de Demarcação/Identificação:</td>\n                <td><label>{{geoidentificacao.tipoDemarcacao.noTipoDemarcacao}}</label></td>\n            </tr>\n            <tr>\n                <td class="td-bold">Etapa:</td>\n                <td><label>{{geoidentificacao.etapaDemarcacao.noEtapaDemarcacao}}</label></td>\n            </tr>\n            <tr ng-show="geoidentificacao.dsTrechoInicio">\n                <td class="td-bold">Trecho Início:</td>\n                <td><label>{{geoidentificacao.dsTrechoInicio}}</label></td>\n            </tr>\n            <tr ng-show="geoidentificacao.dsTrechoFim">\n                <td class="td-bold">Trecho Fim:</td>\n                <td><label>{{geoidentificacao.dsTrechoFim}}</label></td>\n            </tr>\n            <tr ng-show="geoidentificacao.dsTrecho">\n                <td class="td-bold">Trecho:</td>\n                <td><label>{{geoidentificacao.dsTrecho}}</label></td>\n                </tr><tr>\n                    </tr><tr>\n                        <td class="td-bold">UF:</td>\n                        <td><label>{{ufsFormatados}}</label></td>\n                    </tr>\n                    <tr>\n                        <td class="td-bold">Municípios:</td>\n                        <td><label>{{municipiosFormatados}}</label></td>\n                    </tr>\n        </tbody>\n    </table>\n</md-table-container>\n<br>\n<md-table-container>\n    \x3c!--  <md-progress-linear md-mode="query" ng-if="loading.mode === \'query\'"></md-progress-linear>--\x3e\n    <md-progress-linear md-mode="indeterminate" ng-if="loading.mode === \'indeterminate\' && !loading.disabled"></md-progress-linear>\n    <table md-table id="tabela-metadados">\n        <thead md-head>\n            <tr md-row>\n                <th md-column rowspan="2">Data</th>\n                <th md-column rowspan="2">Usuário</th>\n                <th md-column rowspan="1" colspan="3" style="text-align: center !important;">Dados alterados</th>\n            </tr>\n            <tr md-row>\n                <th md-column> Campo</th>\n                <th md-column> Antes</th>\n                <th md-column> Depois</th>\n            </tr>\n        </thead>\n        <tbody md-body ng-repeat="historico in historicos">\n            <tr md-row>\n                <td md-cell class="shine">{{historico.dtAlteracao | date: "dd/MM/yyyy \'às\' HH:mm:ss"}}</td>\n                <td md-cell class="shine">{{historico.noUsuario}}</td>\n                <td md-cell class="shine" colspan="3" style="padding: 0">\n                    <table md-table>\n                        <tbody md-body>\n                            <tr md-row ng-repeat="detalheHistorico in historico.processoDemarcatorioHistoricoDTO">\n                                <td md-cell class="shine" style="width: 33%">\n                                    {{ detalheHistorico.campo }}\n                                </td>\n                                <td md-cell class="shine" style="width: 33%;white-space: pre-line;">\n                                    {{ detalheHistorico.antes }}\n                                </td>\n                                <td md-cell ng-class="{\'color-red\': detalheHistorico.depois === \'removida\'}" style="width: 33%;white-space: pre-line;">\n                                    {{ detalheHistorico.depois }}\n                                </td>\n                            </tr>\n                        </tbody>\n                    </table>\n                </td>\n            </tr>\n        </tbody>\n    </table>\n</md-table-container>\n\x3c!--<md-table-pagination md-limit="10" md-limit-options="[10]" md-page="query.page" md-total="{{total}}"--\x3e\n\x3c!--                     md-on-paginate="buscaHistorico" md-page-select></md-table-pagination>--\x3e\n\n\n<div layout="row" layout-align="end center">\n    <md-button class="md-primary md-raised" ng-click="voltar()">VOLTAR</md-button>\n    <md-button class="md-primary md-raised" ng-click="visualizar(idGeoidentificacao)">VISUALIZAR\n    </md-button>\n</div>'),t.put("scripts-geoidentificacao/geoidentificacao/geoidentificacao_suspender.html",'<md-dialog style="width: 70%;" aria-label="Suspensões">\n    <form name="form" ng-submit="salvar()" class="form-suspender" novalidate>\n        <md-toolbar layout="row" layout-align="start center">\n            <h4 style="margin-left: 10px;">Suspensões</h4>\n        </md-toolbar>\n\n        <md-dialog-content>\n            <md-content layout="row" layout-xs="column">\n                <md-input-container flex="30" flex-xs="100">\n                    <label>Tipo de suspensão</label>\n                    <md-select ng-disabled="isVisualizar" ng-model="geoidentificacaoSuspensao.tipoSuspensao.idTipoSuspensao" name="tipoSuspensao" required>\n                        <md-option ng-repeat="tipo in tipoSuspensoes" ng-value="{{tipo.idTipoSuspensao}}">\n                            {{ tipo.noTipoSuspensao }}\n                        </md-option>\n                    </md-select>\n                    <div ng-messages="form.tipoSuspensao.$error">\n                        <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                    </div>\n                </md-input-container>\n\n                <md-input-container flex="70" flex-xs="100" ng-if="mostrarMotivo">\n                    <label>Motivo da suspensão</label>\n                    <input ng-disabled="isVisualizar" required onkeypress="if(this.value.length == 500) return false" name="motivo" ng-model="geoidentificacaoSuspensao.motivo">\n                    <div ng-messages="form.motivo.$error">\n                        <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                    </div>\n                </md-input-container>\n            </md-content>\n\n            <div layout="column" class="tipo-documento-ato">\n                <div layout="row" class="texto-principal width-100"><b>Publicação do ato/documento suspensivo</b></div>\n                <md-content layout="row" layout-xs="column">\n                    <div flex="30" flex-xs="100" ng-if="!isVisualizar">\n                        <p>Tipo de Documento*</p>\n                        <md-radio-group name="tipoDocumento" layout="column" ng-model="geoidentificacaoSuspensao.tipoDocumento">\n                            <md-radio-button value="PDF" class="md-primary">PDF</md-radio-button>\n                            <md-radio-button value="DOCUMENTO_SEI" class="md-primary">Documento SEI</md-radio-button>\n                        </md-radio-group>\n                    </div>\n\n                    <div ng-if="geoidentificacaoSuspensao.tipoDocumento && !isVisualizar" flex="70" flex-xs="100">\n                        <md-input-container ng-if="geoidentificacaoSuspensao.tipoDocumento === \'PDF\'">\n                            <md-button class="md-primary md-raised" ngf-select="fileChanged($file)">Selecionar Arquivo</md-button>\n                            <span>{{bytteArrayPDF ? bytteArrayPDF.name : \'\'}}</span>\n                        </md-input-container>\n                        <md-input-container flex="100" ng-if="geoidentificacaoSuspensao.tipoDocumento === \'DOCUMENTO_SEI\'">\n                            <label>Número do Documento SEI</label>\n                            <input onkeypress="if(this.value.length == 255) return false" name="processoSEI" ng-model="geoidentificacaoSuspensao.nuProcessoSEI">\n                        </md-input-container>\n                    </div>\n                </md-content>\n                <div layout="row" layout-align="end center" ng-if="!isVisualizar">\n                    <md-button class="md-primary md-raised" aria-label="Limpar" ng-click="limparDocumento()">\n                        Limpar\n                    </md-button>\n                    <md-button class="md-primary md-raised" aria-label="Salvar" ng-click="geoidentificacaoSuspensao.tipoDocumento === \'PDF\' ? adicionarDocumento() : consultaDocumentoSei()">\n                        Adicionar\n                    </md-button>\n                </div>\n                <md-table-container flex="100" ng-if="geoidentificacaoSuspensaoDocumentos.content.length">\n                    <table md-table class="tb-acrescido">\n                        <thead class="md-head">\n                            <th md-cell>\n                                Documento\n                            </th>\n                            <th md-cell ng-if="!isVisualizar">\n                                Ações\n                            </th>\n                        </thead>\n                        <tbody md-body ng-repeat="item in geoidentificacaoSuspensaoDocumentos.content">\n                            <tr md-row>\n                                <td md-cell>\n                                    <button type="button" ng-if="item.tipoArquivo === \'PDF\'" class="btn-download" ng-click="download(item.arquivo, item.noArquivo, \'pdf\')">{{item.noArquivo}}</button>\n                                    <a target="_blank" rel="Link documento SEI" ng-if="item.tipoArquivo === \'PROCESSO_SEI\'" href="{{item.linkProcessoSEI}}">{{item.nuProcessoSEI}}</a></td>\n                                <td md-cell width="100px" ng-if="!isVisualizar">\n                                    <md-button ng-click="removerDocumento(item, $index)" class="md-fab md-raised md-mini" aria-label="Excluir Terreno Acrescido">\n                                        <md-icon ng-bind="\'delete\'">\n                                        </md-icon>\n                                    </md-button>\n                                </td>\n                            </tr>\n                        </tbody>\n                    </table>\n                </md-table-container>\n\n                <md-table-pagination ng-if="geoidentificacaoSuspensaoDocumentos.content.length" md-limit="paginationOptions.size" md-limit-options="[5, 10, 15]" md-page="paginationOptions.page" md-total="{{geoidentificacaoSuspensaoDocumentos.totalElements}}" md-on-paginate="filtrar" md-page-select></md-table-pagination>\n\n            </div>\n\n            <md-content layout="row" layout-xs="column" layout-align="start center">\n                <md-button ng-if="!isVisualizar" flex-xs="100" style="height: fit-content;" ngf-select="uploadFile($file)" class="md-primary md-raised" aria-label="Incluir Arquivo">Importar Área Suspensa\n                  <md-tooltip md-direction="bottom" style="width: 300px;height:120px">\n                    O arquivo shapefile deve estar compactado em um arquivo .zip. O<br>\n                    arquivo .zip deve conter pelo menos os componentes dos arquivos<br>\n                    .shp, .shx, dbf e .prj do shapefile. O arquivo shapefile deve ser<br>\n                    apresentado com DATUM SIRGAS 2000 em coordenadas<br>\n                    geograficas (Código EPSG: 4674).\n                  </md-tooltip>\n                </md-button>\n                <md-button ng-if="isVisualizar" flex-xs="100" style="height: fit-content;" ng-click="download(geoidentificacaoSuspensao.arquivo, geoidentificacaoSuspensao.noArquivo, \'zip\')" class="md-primary md-raised" aria-label="Baixar Geometria da Área Suspensa">\n                    Baixar Geometria da Área Suspensa\n                </md-button>\n                <md-input-container flex="33" flex-xs="100">\n                    <label>Área Aproximada(KM²)</label>\n                    <input disabled="disabled" ng-model="geoidentificacaoSuspensao.areaAproximada" ui-number-mask ui-hide-group-sep>\n                </md-input-container>\n\n                <md-input-container flex="33" flex-xs="100">\n                    <label>Área Oficial(KM²)</label>\n                    <input ui-number-mask ui-hide-group-sep ng-disabled="isVisualizar" ng-model="geoidentificacaoSuspensao.areaOficial" name="areaOficial" required ng-blur="verificarDivergenciaDeArea()">\n                    <div ng-messages="form.areaOficial.$error">\n                        <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                    </div>\n                </md-input-container>\n            </md-content>\n\n            <md-content layout="row" layout-xs="column">\n                <md-input-container flex="50" flex-sm="50" flex-xs="100">\n                    <p>Data de Início da Suspensão*</p>\n                    <md-datepicker ng-model="geoidentificacaoSuspensao.dtInicio" ng-disabled="isVisualizar" required name="dtInicioSuspensao" md-date-locale="monthFormat" md-placeholder="Data" md-open-on-focus="true">\n                    </md-datepicker>\n                    <div ng-messages="form.dtInicioSuspensao.$error">\n                        <div ng-message="required" class="form-error">Campo de preenchimento obrigatório</div>\n                    </div>\n                </md-input-container>\n                <md-input-container ng-if="geoidentificacaoSuspensao.id" flex="50" flex-sm="50" flex-xs="100">\n                    <p>Data do Fim da Suspensão</p>\n                    <md-datepicker ng-model="geoidentificacaoSuspensao.dtFim" ng-disabled="isVisualizar" ng-blur="verificarDataFim()" md-date-locale="monthFormat" md-placeholder="Data" md-open-on-focus="true">\n                    </md-datepicker>\n                </md-input-container>\n            </md-content>\n\n        </md-dialog-content>\n\n        <md-dialog-actions layout="row" layout-align="end center">\n            <md-button class="md-primary md-raised" aria-label="Fechar" ng-click="cancelar()">\n                Fechar\n            </md-button>\n            <md-button ng-if="!isVisualizar" class="md-primary md-raised" aria-label="Limpar" ng-click="limpar()">\n                Limpar\n            </md-button>\n            <md-button ng-if="!isVisualizar" type="submit" class="md-primary md-raised" aria-label="Salvar">\n                Salvar\n            </md-button>\n        </md-dialog-actions>\n    </form>\n</md-dialog>'),t.put("scripts-geoidentificacao/geoidentificacao/geoidentificacao_tabela.html",'\n\n    <div class="su-geoidentificacao-tab" style="overflow: auto; width: 300px; height: 98px; border: solid 3px color: black;">\n\n      <table class="tabela" style="width:100px">\n        <thead>\n          <tr>\n            <th>UF(s):</th>\n            <th>Municípios(s):</th>\n\n          </tr>\n        </thead>\n        <tbody>\n          <tr>\n            <td>Go</td>\n            <td>Campos Belos</td>\n\n          </tr>\n          <tr>\n            <td>TO</td>\n            <td>Arraias Paranã</td>\n\n          </tr>\n          <tr>\n            <td>DF</td>\n            <td>Gama</td>\n\n          </tr>\n          <tr>\n            <td>MG</td>\n            <td>Paracatu</td>\n\n          </tr>\n          <tr>\n            <td>SP</td>\n            <td>Diadema</td>\n\n          </tr>\n          <tr>\n            <td>RJ</td>\n            <td>Macaé</td>\n\n          </tr>\n          <tr>\n            <td>PI</td>\n            <td>pParnaíba</td>\n\n          </tr>\n          <tr>\n            <td>GO</td>\n            <td>Jd Zuleika</td>\n\n          </tr>\n          <tr>\n            <td>GO</td>\n            <td>Jd Planalto</td>\n\n          </tr>\n        </tbody>\n      </table>\n    </div>\n\n\n  \n\n\n\n'),t.put("scripts-geoidentificacao/geoidentificacao/geoidentificacao_visualizar.html",'<style>\n    span {\n        font-weight: bold;\n    }\n    \n    #geoidentificacao-visualizar tr.espaco-embaixo>td {\n        padding-bottom: 2em;\n    }\n    \n    #geoidentificacao-visualizar tr :first-child {\n        text-align: right;\n    }\n    \n    #geoidentificacao-visualizar tr :last-child {\n        text-align: left;\n    }\n</style>\n\n<div id="geoidentificacao-visualizar">\n    <div class="su-geoidentificacao-texto-principal">Visualizar Geoidentificação de Áreas da União</div>\n    <v-accordion class="vAccordion--default" id="my-accordion" multiple="multiple" control="accordion" style="margin-bottom: 35px;">\n        <v-pane expanded>\n            <v-pane-header></v-pane-header>\n            <v-pane-content>\n                <div layout-align="center center" flex="100" layout-margin layout-wrap style="height: 500px;">\n                    <su-map map-id="map-geoidentificacao" mviewer="mviewer" mostrar-query="mostrarQuery">\n                        <div style="display: flex; position: relative;padding: 0.3em;" layout-align="center center">\n                            <b>LEGENDA:</b>&emsp;\n                            <div style="display: flex;" ng-if="controleLegenda.marinha">\n                                <div class="retangulo marinha"></div>&ensp; Terreno de Marinha &emsp;\n                            </div>\n                            <div style="display: flex;" ng-if="controleLegenda.acrescidoMarinha">\n                                <div class="retangulo acrescidoMarinha"></div>&ensp; Acrescido de Marinha&emsp;\n                            </div>\n                            <div style="display: flex;" ng-if="controleLegenda.marginal">\n                                <div class="retangulo marginal"></div>&ensp; Terreno Marginal&emsp;\n                            </div>\n                            <div style="display: flex;" ng-if="controleLegenda.acrescidoMarginal">\n                                <div class="retangulo acrescidoMarginal"></div>&ensp; Acrescido Marginal&emsp;\n                            </div>\n                            <div style="display: flex;" ng-if="controleLegenda.suspenso">\n                                <div class="retangulo suspenso"></div>&ensp; Área suspensa&emsp;\n                            </div>\n                            <div style="display: flex;" ng-if="controleLegenda.sobreposta">\n                                <div class="retangulo sobreposta"></div>&ensp; Área sobreposta&emsp;\n                            </div>\n                            <div style="display: flex;" ng-if="controleLegenda.invalido">\n                                <div class="retangulo invalido"></div>&ensp; Área inválida &emsp;\n                            </div>\n                            <div style="display: flex;" ng-if="controleLegenda.outro">\n                                <div class="retangulo outro"></div>&ensp; Outros &emsp;\n                            </div>\n                            <div style="display: flex;" ng-if="controleLegenda.naoDemarcado">\n                                <div class="retangulo rachurada"></div>&ensp; Não Demarcado &emsp;\n                            </div>\n                        </div>\n                    </su-map>\n                </div>\n                <br>\n                <div layout="row" layout-margin class="identificacao" layout-xs="column" ng-show="showCheckVisualizarProdCartGeom">\n                    <md-checkbox ng-model="produtoCartVisualizar" ng-change="visibilidadeGeomProdCart(produtoCartVisualizar)">\n                        Visualizar Produtos Cartográficos</md-checkbox>\n                </div>\n            </v-pane-content>\n        </v-pane>\n    </v-accordion>\n\n    <md-table-container style="overflow: auto">\n        <table>\n            <tbody>\n                <tr>\n                    <td><span>Nº do Processo de Identificação: </span></td>\n                    <td><label>{{geoinformacao.nuProcessoDemarcatorio}}</label></td>\n                </tr>\n                <tr>\n                    <td><span>Nível de precisão cartográfica: </span></td>\n                    <td><label>{{geoinformacao.niveisPrecisaoCartografica.dsNiveisPrecisaoCartografica}}</label></td>\n                </tr>\n                <tr class="espaco-embaixo">\n                    <td><span>Portaria de Comissão de Demarcação: </span></td>\n                    <td><label>{{portarias}}</label></td>\n                </tr>\n                <tr>\n                    <td><span>Tipo de ação de demarcação/identificação: </span></td>\n                    <td><label>{{geoinformacao.tipoDemarcacao.noTipoDemarcacao}}</label></td>\n                </tr>\n                <tr>\n                    <td><span>Etapa: </span></td>\n                    <td><label>{{geoinformacao.etapaDemarcacao.noEtapaDemarcacao}}</label></td>\n                </tr>\n                <tr>\n                    <td><span>Demanda geradora do processo:</span></td>\n                    <td><label>{{geoinformacao.finalidadeDemarcacao.noFinalidadeDemarcacao}}</label></td>\n                </tr>\n                <tr class="espaco-embaixo">\n                    <td><span>Situação: </span></td>\n                    <td><label>{{geoinformacao.situacao}}</label></td>\n                </tr>\n                <tr>\n                    <td><span> Localização </span></td>\n                    <td></td>\n                </tr>\n                <tr ng-show="geoinformacao.dsTrechoInicio">\n                    <td style="padding-left: 30px"><span>Trecho Início: </span></td>\n                    <td><label>{{geoinformacao.dsTrechoInicio}}</label></td>\n                </tr>\n                <tr ng-show="geoinformacao.dsTrechoFim" class="espaco-embaixo">\n                    <td style="padding-left: 30px"><span>Trecho Fim: </span></td>\n                    <td><label>{{geoinformacao.dsTrechoFim}}</label></td>\n                </tr>\n                <tr ng-show="geoinformacao.dsTrecho" class="espaco-embaixo">\n                    <td style="padding-left: 30px"><span>Trecho: </span></td>\n                    <td><label>{{geoinformacao.dsTrecho}}</label></td>\n                    </tr><tr>\n                        <td><span>UF: </span></td>\n                        <td><label>{{ufsFormatados}}</label></td>\n                    </tr>\n                    <tr class="espaco-embaixo">\n                        <td><span>Município: </span></td>\n                        <td>\n                            <label>{{ municipiosFormatados }}</label>\n                        </td>\n                    </tr>\n                    <tr>\n                        <td><span>Área Aproximada (km²): </span></td>\n                        <td> <label>{{ metrosParaKilometrosQuadrados(geoinformacao.areaAproximada) }}</label> </td>\n                    </tr>\n                    <tr>\n                        <td><span>Área Oficial (km²): </span></td>\n                        <td> <label>{{ metrosParaKilometrosQuadrados(geoinformacao.areaOficial) }}</label> </td>\n                    </tr>\n                    <tr class="espaco-embaixo">\n                        <td><span>Observação: </span></td>\n                        <td><label>{{geoinformacao.dsObservacao}}</label></td>\n                    </tr>\n\n                    <tr>\n                        <td><span>Esta Geoidentificação de Área da União possui produtos cartográficos associados a ela? </span></td>\n                        <td><label>{{geoinformacao.produtosRelacionados}}</label></td>\n                    </tr>\n                    <tr class="espaco-embaixo">\n                        <td><span>Justificativa:</span></td>\n                        <td><label>{{geoinformacao.dsJustificativaGeometadados}}</label></td>\n                    </tr>\n\n                    <tr ng-if="mostrarInfoTerrenoAcrescido()">\n                        <td><span>Terreno Acrescido</span></td>\n                        <td></td>\n                    </tr>\n                    <tr ng-if="mostrarInfoTerrenoAcrescido()">\n                        <td><span>Área Aproximada (km²)</span></td>\n                        <td><label>{{areaAcrescida.areaAproximada.toFixed(2)}} Km²</label></td>\n                    </tr>\n                    <tr class="espaco-embaixo" ng-if="mostrarInfoTerrenoAcrescido()">\n                        <td><span>Área Oficial (km²)</span></td>\n                        <td><label>{{areaAcrescida.areaOficial.toFixed(2)}} Km²</label></td>\n                    </tr>\n            </tbody>\n\n            <tbody ng-if="areasSuspensas.length > 0">\n                <tr>\n                    <td><span>Suspensões</span></td>\n                    <td></td>\n                </tr>\n            </tbody>\n\n            <tbody ng-repeat="suspensao in areasSuspensas">\n                <tr>\n                    <td><span>Tipo: </span></td>\n                    <td><label>{{suspensao.tipoSuspensao.noTipoSuspensao}}</label></td>\n                </tr>\n                <tr>\n                    <td><span>Motivo: </span></td>\n                    <td><label>{{suspensao.motivo}}</label></td>\n                </tr>\n                <tr>\n                    <td><span>Área do Polígono (km²): </span></td>\n                    <td><label>{{suspensao.areaAproximada.toFixed(2)}}</label></td>\n                </tr>\n                <tr>\n                    <td><span>Área Oficial (km²): </span></td>\n                    <td><label>{{suspensao.areaOficial.toFixed(2)}}</label></td>\n                </tr>\n                <tr>\n                    <td><span>Data de Início da Suspensão: </span></td>\n                    <td><label>{{suspensao.dtInicio | date: \'dd/MM/yyyy\'}}</label></td>\n                </tr>\n                <tr>\n                    <td><span>Data de Fim da Suspensão: </span></td>\n                    <td><label>{{suspensao.dtFim | date: \'dd/MM/yyyy\'}}</label></td>\n                </tr>\n                <tr>\n                    <td><span>Publicão do ato/documento suspensivo: </span></td>\n                    <td>\n                        <a ng-if="suspensao.documentoSuspensaoMetadados.tipoArquivo === \'PROCESSO_SEI\'" ng-href="{{ suspensao.documentoSuspensaoMetadados.linkProcessoSEI }}" target="_blank"> {{ suspensao.documentoSuspensaoMetadados.nuProcessoSEI }} </a>\n                        <a ng-if="suspensao.documentoSuspensaoMetadados.tipoArquivo === \'PDF\'" ng-href="{{suspensao.documentoSuspensaoMetadados.href}}" target="_blank" download> {{ suspensao.documentoSuspensaoMetadados.noArquivo }} </a>\n                    </td>\n                </tr>\n            </tbody>\n        </table>\n    </md-table-container>\n\n    <div layout="row" layout-align="end center">\n        <md-button class="md-primary md-raised" ng-click="voltar()">VOLTAR</md-button>\n        <md-button ng-show="authenticatedEditar == true" class="md-primary md-raised" ui-sref="GEOIDENTIFICACAO.geoidentificacaoeditar({id: geoinformacao.idProcessoDemarcatorio})">EDITAR</md-button>\n        <md-button ng-show="authenticatedEditar == false" class="md-primary -md-raised" ng-click="alertPermissao(\'editar\');">EDITAR</md-button>\n    </div>\n</div>'),t.put("scripts-geoidentificacao/geoidentificacao/painel_atributo.html",'<div role="dialog" aria-label="Atributos" layout="column" layout-align="center center">\n  <md-toolbar>\n    <div class="md-toolbar-tools">\n      <h2>Atributos</h2>\n    </div>\n  </md-toolbar>\n\t\t<div class="demo-dialog-content" style="padding: 1px 20px 10px 20px; overflow: auto">\n\t\t\t\t\t  <div layout="row">\n\t\t\t\t\t      <p><b>Nº do Processo de Identificação:</b> {{listAtributos.dsTrechoFim}}</p>\n\t\t\t\t\t  </div>\n\t\t\t\t\t  <div layout="row">\n\t\t            <p><b>Tipo:</b> {{listAtributos.tipoDemarcacao}}</p>\n\t\t        </div>\n\t\t        <div layout="row">\n\t\t            <p><b>Etapa:</b> {{listAtributos.etapaDemarcacao}}</p>\n\t\t        </div>\n\t\t        \n\t\t        <div layout="row">\n\t\t            <p><b>Finalidade:</b> {{listAtributos.finalidadeDemarcacao}}</p>\n\t\t        </div>\n\t\t        <div layout="row">\n\t\t            <p><b>Cadastrador:</b> {{listAtributos.cadastrador}}</p>\n\t\t        </div>\n\t\t        \n\t\t        <fieldset style="border-style: solid; border-width: 1px;">\n              <legend style="font-weight: bold;">Localização</legend>\n              <div layout="row">\n\t                <p><b>Trecho Início:</b> {{listAtributos.dsTrechoInicio}}</p>\n\t            </div>\n\t            <div layout="row">\n\t                <p><b>Trecho Fim:</b> {{listAtributos.dsTrechoFim}}</p>\n\t            </div>\n            </fieldset>\n\t\t        \n\t\t        <div layout="row">\n\t\t            <p><b>UF:</b>&nbsp</p>\n                <p ng-repeat="uf in listAtributos.ufs">{{uf}},&nbsp</p>\n\t\t        </div>\n\t\t        <div layout="row">\n\t\t            <p><b>Municípios:</b>&nbsp</p>\n\t\t            <p ng-repeat="municipio in listAtributos.municipios">{{municipio.noAreaPoliticoAdministrativa}},&nbsp</p>\n\t\t        </div>\n\t\t        <div layout="row">\n\t\t            <p><b>Data do Cadastro:</b> {{listAtributos.dtAlteracao}}</p>\n\t\t        </div>\n\t\t        <div layout="row">\n\t\t            <p><b>Observação:</b> {{listAtributos.dsObservacao}}</p>\n\t\t        </div>\n\t\t\t  </div>\n</div>')}]),function(){"use strict";function t(t){t.state("GEOIDENTIFICACAO",{url:"/geoidentificacao",template:'<ui-view id="su-geoidentificacao"></ui-view>',abstract:!0,ncyBreadcrumb:{label:"HOME"}}).state("GEOIDENTIFICACAO.geoidentificacaoconsultar",{url:"",component:"geoidentificacaoConsultar",ncyBreadcrumb:{label:"GEOIDENTIFICAÇÃO"}}).state("GEOIDENTIFICACAO.geoidentificacaocadastrar",{url:"/cadastrar",component:"geoidentificacaocadastrar",ncyBreadcrumb:{label:"CADASTRAR",parent:"GEOIDENTIFICACAO.geoidentificacaoconsultar"}}).state("GEOIDENTIFICACAO.geoidentificacaovisualizar",{url:"/:id",component:"geoidentificacaovisualizar",ncyBreadcrumb:{label:"VISUALIZAR",parent:"GEOIDENTIFICACAO.geoidentificacaoconsultar"}}).state("GEOIDENTIFICACAO.geoidentificacaoeditar",{url:"/editar/:id",component:"geoidentificacaoeditar",ncyBreadcrumb:{label:"EDITAR",parent:"GEOIDENTIFICACAO.geoidentificacaoconsultar"}}).state("GEOIDENTIFICACAO.historico",{url:"/buscarHistorico/:id",component:"geoidentificacaoHistorico"})}t.$inject=["$stateProvider"],angular.module("su-geoidentificacao").config(t)}();
(function () {
  'use strict';

  angular.module('su-servicos', [
    'su-componentes']);

})();

(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('unidadeSeiService', ['$http', 'comumServiceServico', '$filter',
      function ($http, comumServiceServico, $filter) {

        var url = '/servicos/api/unidade-sei';

        var findById = function (id) {
          return $http.get(url + '/' + id);
        };

        var save = function (unidade) {
          if (unidade.id) {
            return $http.put(url, unidade);
          } else {
            return $http.post(url, unidade);
          }
        };

        var deletar = function (id) {
          return $http.delete(url + '/' + id);
        };

        var findAll = function (offset, limit) {
          return $http.get(url, {
            params: {
              offset: offset,
              limit: limit
            }
          });
        };

        var filterFindAll = function (codigo, sigla, uf, descricao, offset, limit) {
          codigo = codigo != undefined ? codigo : null;
          sigla = sigla != undefined ? sigla : null;
          uf = uf != undefined ? uf : null;
          descricao = descricao != undefined ? descricao : null;
          
          return $http.get(url, {
            params: {
              codigo: codigo,
              sigla: sigla,
              uf: uf,
              descricao: descricao,
              offset: offset,
              limit: limit
            }
          });
        };

        var listarUnidadeSei = function () {
          return $http.get(urlBase + '/listar');
        };

        var pesquisarPorGrupoUnidadeSei = function (idGrupoUnidadeSei) {
          return $http.get(url + '/pesquisar/grupo-sei/'+idGrupoUnidadeSei);
        };

        var unidadesPorGrupoPaginado = function (idGrupoUnidadeSei,  offset, limit) {
          return $http.get(url + '/pesquisar/grupo-sei-paginado/' , {
            params: {
              idGrupoUnidadeSei: idGrupoUnidadeSei,
              offset: offset,
              limit: limit
            }
          });
        };

        var getDescricao = function () {
          return $http.get(url + '/descricao');
        };

        var getSiglasUnidadesPorUf = function (uf) {
          return $http.get(url + '/siglas/uf/'+uf);
        };


        var validarMinLength = function (model, msg) {
          var texto = comumServiceServico.htmlToPlaintext(model);

          if (texto.length < 10) {
            comumServiceServico.mostrarMensagemError($filter('translate')('error-min-caracteres', {
              max: '10'
            }));
            return false;
          }

          return true;
        };

        var setarOrdem = function (lista) {
          angular.forEach(lista, function (item, $index) {
            item.ordem = $index;
          });
        };

        var ordenaLista = function (atual, destino, lista) {
          var item = lista[atual];
          lista[atual] = lista[destino];
          lista[destino] = item;
        };

        var verificarExisteCampo = function (mensagens, campo) {
          var existe = false;
          angular.forEach(mensagens, function (item) {
            if (item == campo)
              existe = true;
          });
          return existe;
        };

        var apresentarCamposInvalidos = function (form) {

          var error = form.$error;
          var mensagens = [];
          angular.forEach(error.required, function (field) {
            if (field.$invalid) {
              if (!verificarExisteCampo(mensagens, field.$name))
                mensagens.push(field.$name);
            }
          });

          if (mensagens.length > 0) {
                comumServiceServico.mostrarMensagemError('Necessário preencher os campos obrigatórios');
          }
        };

        var getQueryDefault = function () {
            return angular.copy({
              order: 'tipo',
              limit: 10,
              limitsPage: [5, 10, 20],
              page: 1
            });
        };

        var getQueryNome= function () {
          return angular.copy({
            order: 'nome',
            limit: 10,
            limitsPage: [5, 10, 20],
            page: 1
          });
        };

        return {
          findById: findById,
          save: save,
          deletar: deletar,
          findAll: findAll,
          filterFindAll: filterFindAll,
          listarUnidadeSei: listarUnidadeSei,
          pesquisarPorGrupoUnidadeSei: pesquisarPorGrupoUnidadeSei,
          unidadesPorGrupoPaginado: unidadesPorGrupoPaginado,
          getDescricao: getDescricao,
          getSiglasUnidadesPorUf: getSiglasUnidadesPorUf,
          validarMinLength: validarMinLength,
          setarOrdem: setarOrdem,
          ordenaLista: ordenaLista,
          verificarExisteCampo: verificarExisteCampo,
          apresentarCamposInvalidos: apresentarCamposInvalidos,
          getQueryDefault: getQueryDefault,
          getQueryNome: getQueryNome,
        };
      }])

})();
(function(){

    'use strict';

    angular
        .module('su-servicos')
        .controller('unidadeSeiListController',  ['$scope','unidadeSeiService','$state', '$mdDialog','comumServiceServico',
        function ($scope, unidadeSeiService, $state, $mdDialog,comumServiceServico) {

        var init = function() {
            $scope.unidades = [];
            $scope.minDate = new Date();
            $scope.registroInicial = 1;
            $scope.tamanhoLimite = 5;
            $scope.totalItems = 0;
            $scope.updateTable();

            comumServiceServico.listarTodasUfs().then(function (resposta) {
                $scope.listUfs = resposta.data.resposta;
            });

            unidadeSeiService.getDescricao().then(function (resposta) {
                $scope.listaDescricoes = resposta.data.resposta;
              });

        };

        $scope.updateTable = function(){
            $scope.carregando = true;
            unidadeSeiService.findAll(($scope.registroInicial - 1), $scope.tamanhoLimite).then(function (resp) {
                $scope.unidades = resp.data.resposta[0].content;
                $scope.totalItems = resp.data.resposta[0].totalElements;
            });
        };

        $scope.filterFindAll = function() {
            unidadeSeiService.filterFindAll($scope.codigoSei, $scope.sigla, $scope.uf, $scope.descricao, ($scope.registroInicial - 1), $scope.tamanhoLimite).then(function(resp) {
                $scope.unidades = resp.data.resposta[0].content;
                $scope.totalItems = resp.data.resposta[0].totalElements;
            });
        };

        $scope.findPagination = function() {
            if ( ($scope.codigoSei != undefined && $scope.codigoSei != "")
                 || ($scope.sigla != undefined && $scope.sigla != "")
                 || ($scope.uf != undefined && $scope.uf != "")  
                 || ($scope.descricao != undefined && $scope.descricao != "")           
            ) {
                $scope.filterFindAll();
            } else {
                $scope.updateTable();
            }
        };

        $scope.adicionarUnidade = function(e){
          e.preventDefault();
            $state.go('servicos.SERVICOS_CADASTRAR_UNIDADE_SEI');
        };

        $scope.clearFilter = function() {
            $scope.codigoSei = "";
            $scope.sigla = "";
            $scope.uf = "";
            $scope.descricao = "";
        };

        $scope.showConfirmExcluir = function(ev, id) {
            var confirm = $mdDialog.confirm({focusOnOpen:false})
                .title('Excluir Unidade Sei')
                .textContent('Deseja excluir dados?')
                .targetEvent(ev)
                .ok('Sim')
                .cancel('Não');
            $mdDialog.show(confirm).then(function() {
                unidadeSeiService.deletar(id).then(function(resposta){
                    $scope.updateTable();
                });
            }, function() {
                $mdDialog.cancel();
            });
        };

        $scope.novoCadastro = function() {
            $state.go('servicos.SERVICOS_CADASTRAR_UNIDADE_SEI');
        };

        init();

    }]);

})();
(function(){

    'use strict';

    angular
        .module('su-servicos')
        .controller('detalharUnidadeSeiController',
                      ['$scope', '$stateParams', '$state', 'unidadeSeiService', 
                      'comumServiceServico', '$filter', 'grupoProcessoSeiService',
                      '$element',
            function ($scope, $stateParams, $state, unidadeSeiService, 
                      comumServiceServico, $filter, grupoProcessoSeiService,
                      $element) {

            $element.find('input').on('keydown', function(ev) {
                ev.stopPropagation();
            });

            var vm = $scope;

            var init = function () {


                comumServiceServico.listarTodasUfs().then(function (resposta) {
                  $scope.listUfs = resposta.data.resposta;
                });
                grupoProcessoSeiService.listarGruposUnidadeSei().then(function (resposta) {
                  $scope.listGrupos = resposta.data.resposta;
                });

                vm.selected = [];

                vm.tituloStatico = $filter('translate')('label-titulo-detalhar');

                unidadeSeiService.findById($stateParams.id).then(function (resposta) {

                    getDados(resposta);

                    $scope.editando = true;

                });
               
            };

            var getDados = function (resposta) {
                vm.unidade = resposta.data.resposta[0];

            };

            vm.mostrarResultado = function (box, num_max, campospan) {
              document.getElementById(campospan).innerHTML = box.length + " / 5";
            };

            init();

        }]);

})();
(function(){

    'use strict';

    angular
        .module('su-servicos')
        .controller('cadastrarUnidadeSeiController',
                      ['$scope', '$stateParams', '$state', 'unidadeSeiService', 
                      'comumServiceServico', '$filter', 'grupoProcessoSeiService',
                      '$element',
            function ($scope, $stateParams, $state, unidadeSeiService, 
                      comumServiceServico, $filter, grupoProcessoSeiService,
                      $element) {

            $element.find('input').on('keydown', function(ev) {
                ev.stopPropagation();
            });

            var vm = $scope;
            vm.isNew = !($stateParams.id);
            vm.novaDescricao=false;

            var init = function () {

                $scope.searchTerm;
                $scope.clearSearchTerm = function() {
                  $scope.searchTerm = '';
                };

                comumServiceServico.listarTodasUfs().then(function (resposta) {
                  $scope.listUfs = resposta.data.resposta;
                });
                grupoProcessoSeiService.listarGruposUnidadeSei().then(function (resposta) {
                  $scope.listGrupos = resposta.data.resposta;
                });

                unidadeSeiService.getDescricao().then(function (resposta) {
                  $scope.listaDescricoes = resposta.data.resposta;
                });

                vm.selected = [];

                if (vm.isNew) {
                    vm.reset();
                    $scope.editando = false;
                } else {

                    vm.tituloStatico = $filter('translate')('label-titulo');

                    unidadeSeiService.findById($stateParams.id).then(function (resposta) {

                        getDados(resposta);

                        $scope.editando = true;

                    });
                }

            };

            var getDados = function (resposta) {
                vm.unidade = resposta.data.resposta[0];

            };

            vm.reset = function () {

                vm.unidade = {
                  descricao: '',
                  email: 'cgtec.spu@gestao.gov.br'
                };

                if (angular.isDefined(vm.manterUnidadeSei)) {
                    vm.manterUnidadeSei.$setUntouched();
                }
            };

            vm.mostrarResultado = function (box, num_max, campospan) {
              document.getElementById(campospan).innerHTML = box.length + " / 5";
            };

            vm.cadastrarDescricao = function (){
              vm.novaDescricao = true;
            }

            function indexOf(lista, elemento) {
              for (var i = 0; i < lista.length; i++) {
                if (lista[i].id === elemento.id) {
                   return i;
                }
              }
            }

            vm.validar = function(){
              var valido = vm.form.$valid;
              if (!valido) {
                 servicoService.apresentarCamposInvalidos(vm.form);
              }
              return valido;
            };

            vm.salvar = function () {
              unidadeSeiService.save(vm.unidade).then(function (resposta) {

                    comumServiceServico.mostrarMensagemSucesso('Dados gravados com sucesso');

                    if (!vm.isNew) {
                      getDados(resposta);
                    }

                    $state.go('servicos.SERVICOS_CONSULTAR_UNIDADE_SEI');
                    vm.reset();

                }, function (erro) {

                    var msg = (erro.data.mensagem) ? erro.data.mensagem[0] : erro.data.error;

                    comumServiceServico.mostrarMensagemError(msg);
                });
            };

            vm.filtraUnidade = function(filtro) {
              vm.unidade.descricao = filtro;
              //return vm.listaDescricoes.filter((desc) => desc.toUpperCase().indexOf(filtro.toUpperCase()) > -1);
              return vm.listaDescricoes.filter(vm.pesquisarUnidade);       

            };

            vm.pesquisarUnidade = function(desc){
              return desc.toUpperCase().indexOf(vm.unidade.descricao.toUpperCase()) > -1;
            }

            init();

        }]);

})();

(function () {

    'use strict';

    triagemRequerimentoService.$inject = ["$http", "escopoCompartilhadoService", "$state"];
    angular
        .module('su-servicos')
        .factory('triagemRequerimentoService', triagemRequerimentoService);

    function triagemRequerimentoService($http, escopoCompartilhadoService, $state) {

        var url = '/servicos/api/requerimento';

        var pausarAtendimento = function(numeroAtendimento, qntDias, justificativa){
          return $http.get(url + '/pausarAtendimento/'+qntDias+'/'+numeroAtendimento.replace('/', '_')+'/'+justificativa);
        };

        var despausarAtendimento = function(numeroAtendimento){
          return $http.get(url + '/despausarAtendimento/'+numeroAtendimento.replace('/', '_'));
        };

        var findAllStatus = function () {
            return $http.get(url + '/statusRequerimento');
        };

        var findById = function(id){
            return $http.get(url + '/' + id);
        };

        var findAll = function (offset, limit) {
            return $http.get('/servicos/api/public/requerimento/findAll', {
                params: {
                    offset: offset,
                    limit: limit
                }
            });
        };

        var filterFindAll = function(filtro, offset, limit) {

            if (filtro) {
            return pesquisarComParametros(filtro, offset, limit);
            } else {
            return pesquisar(filtro, offset, limit);
            }

        };

        var pesquisarComParametros = function (filtro, offset, limit) {

            var formatDate = function(date){
                return date.replace(new RegExp("/", 'g'), "-");
            };

            if(filtro.dataDe != null  &&  filtro.dataAte == null){
                filtro.dataDeTxt = formatDate(filtro.dataDe.toLocaleDateString());
            } else if(filtro.dataAte != null && filtro.dataDe == null) {
                filtro.dataAteTxt = formatDate(filtro.dataAte.toLocaleDateString());
            } else if(filtro.dataAte != null && filtro.dataDe != null) {
                filtro.dataDeTxt = formatDate(filtro.dataDe.toLocaleDateString());
                filtro.dataAteTxt = formatDate(filtro.dataAte.toLocaleDateString());
            } else {
                filtro.dataDeTxt = "";
                filtro.dataAteTxt = "";
            }

            return pesquisar(filtro, offset, limit);
        };

        var pesquisar = function (filtro, offset, limit) {
        return $http.get(url + '/search', {
            params: {
                nomeRequerente: filtro.nomeRequerente ,
                servicoId: filtro.servicoId,
                cpfCnpj: filtro.cpfCnpj,
                nuAtendimento: filtro.nuAtendimento,
                statusRequerimento: filtro.statusRequerimento,
                unidadesAtendimento: filtro.unidadesAtendimento,
                ufImovel: filtro.ufImovel,
                prioridadeAtendimento: filtro.prioridadeAtendimento,
                offset: offset,
                limit: limit,
                tipoPesquisaAtendimento: filtro.tipoPesquisaAtendimento,
                dataDe: filtro.dataDeTxt,
                dataAte: filtro.dataAteTxt,
                tipoDeOrdenacao: filtro.tipoDeOrdenacao,
                colunaOrdenacao: filtro.colunaOrdenacao
            }
        });
        };

        var addAnotacoes = function(requerimento){
            // mudar - o campo anotacao não e a anotacao do requetimento
            //return $http.put(url + "/addanotacoes", requerimento);
        };

        var downloadArquivo = function(idRequerimento, idDocumento){
            return $http.get(url + "/download/arquivo-documento", {
                params: {
                    idRequerimento: idRequerimento,
                    idDocumento: idDocumento
                }
            });
        };

        var irParaDetalhar = function(item) {
            escopoCompartilhadoService.setObjetos('codigoIdentificacao', item.codigoIdentificacao);
            $state.go('servicos.' + item.rota + '/detalhar');
        };

        var cancelarEnvioAutomaticoSei = function (idRequerimento, numeroProcesso) {
            return $http.delete(url + '/' + idRequerimento +'/'+ numeroProcesso);
        };

        var getBuscarProcessoNaFila = function (idRequerimento) {
            return $http.get(url + '/getBuscarProcessoNaFila/' + idRequerimento);
        };

        return {
                getBuscarProcessoNaFila:getBuscarProcessoNaFila,
                cancelarEnvioAutomaticoSei:cancelarEnvioAutomaticoSei,
                findById: findById,
                addAnotacoes: addAnotacoes,
                findAllStatus: findAllStatus,
                findAll: findAll,
                filterFindAll: filterFindAll,
                downloadArquivo: downloadArquivo,
                irParaDetalhar: irParaDetalhar,
                pausarAtendimento: pausarAtendimento,
                despausarAtendimento: despausarAtendimento
            };

    }


})();

(function () {

    'use strict';

    controller.$inject = ["$scope", "triagemRequerimentoService", "servicoService", "$mdDialog", "$location", "$q", "$rootScope", "$element", "comumServiceServico", "ComumFactory", "$filter", "$state"];
    angular
        .module('su-servicos')
        .controller('triagemRequerimentoListController', controller);

        function controller($scope, triagemRequerimentoService, servicoService,
                            $mdDialog, $location, $q, $rootScope,$element, comumServiceServico, ComumFactory,$filter, $state) {

        $element.find('input').on('keydown', function(ev) {
            ev.stopPropagation();
        });

        var vm = $scope;

          vm.ufDoAtendimento = true;
          vm.interessado = true;
          vm.nomeDoServico = true;
          vm.numeroDoAtendimento = true;
          vm.dataDoRequerimento = true;

          vm.sortField = undefined;
          vm.reverse = false;

          vm.isSortUp = function(fieldName) {
            return $scope.sortField === fieldName && !$scope.reverse;
          };

          vm.isSortDown = function(fieldName) {
            return $scope.sortField === fieldName && $scope.reverse;
          };

          function mostrarIcone(fieldName) {
            if(fieldName === "ufDoAtendimento") {
              vm.ufDoAtendimento = false;
            } else {
              vm.ufDoAtendimento = true;
            }

            if(fieldName === "interessado") {
              vm.interessado = false;
            } else {
              vm.interessado = true;
            }

            if(fieldName === "nomeDoServico") {
              vm.nomeDoServico = false;
            } else {
              vm.nomeDoServico = true;
            }

            if(fieldName === "numeroDoAtendimento") {
              vm.numeroDoAtendimento = false;
            } else {
              vm.numeroDoAtendimento = true;
            }

            if(fieldName === "dataDoRequerimento") {
              vm.dataDoRequerimento = false;
            } else {
              vm.dataDoRequerimento = true;
            }

          };


          function mostrarFiltroDefault() {

            vm.ufDoAtendimento = true;
            vm.interessado = true;
            vm.nomeDoServico = true;
            vm.numeroDoAtendimento = true;
            vm.dataDoRequerimento = true;

            vm.sortField = undefined;
            vm.filtro.orderByColuna = "";
            vm.filtro.tipoDeOrdenacao = "";

          };

          vm.sort = function(fieldName) {

            mostrarIcone(fieldName);
            //console.log(vm.filtro.tipoDeOrdenacao);
            if (!vm.filtro.tipoDeOrdenacao) {
              vm.filtro.tipoDeOrdenacao = "desc";
            }

            if ($scope.sortField === fieldName) {
              vm.reverse = !$scope.reverse;
              vm.filtro.colunaOrdenacao = fieldName;

            } else {
              vm.sortField = fieldName;
              vm.reverse = false;
              vm.filtro.colunaOrdenacao = fieldName;

            }

            vm.filterFindAll();

            vm.filtro.colunaOrdenacao = null;

            vm.filtro.tipoDeOrdenacao = !vm.reverse ? "asc" : "desc";

            //console.log(vm.filtro)

          };



        vm.irParaDetalhar = triagemRequerimentoService.irParaDetalhar;

        vm.filtro = {
          filtroPrazoAtendimento: {},
          tipoPesquisaAtendimento: 'RECEBIDOS_NO_PERIODO'
        };

        vm.dataAtual = new Date();

        vm.isVencido = function(){
          return vm.filtro.tipoPesquisaAtendimento.indexOf('VENCIDOS') >= 0;
        };

        vm.isRecebidos = function(){
          return vm.filtro.tipoPesquisaAtendimento.indexOf('RECEBIDOS') >= 0;
        };

        vm.changeSituacao = function(){
          if(vm.filtro.statusRequerimento){
            vm.filtro.tipoPesquisaAtendimento = 'RECEBIDOS_NO_PERIODO';
          }
        };

        vm.changeTipo = function(){
          if(vm.filtro.tipoPesquisaAtendimento != 'RECEBIDOS_NO_PERIODO'){
            vm.filtro.statusRequerimento = undefined;
          }
          vm.filtro.dataDe = undefined;
          vm.filtro.dataAte = undefined;

        };

        var init = function() {

            vm.requerimentos = [];
            vm.registroInicial = 1;
            vm.tamanhoLimite = 5;
            vm.totalItems = 0;
            vm.filtro.statusRequerimento = 'AGUARDANDO_ANALISE_PREVIA';

            //console.log($rootScope.usuarioLogado)

            comumServiceServico.listarTodasUfs().then(function (resposta) {
                vm.listUfs = resposta.data.resposta;
            });

            var promises = [
                servicoService.pesquisarTituloServico(),
                triagemRequerimentoService.findAllStatus()
            ];

            $q.all(promises).then(function(res){
                    if(res[0]){
                         vm.servicos = res[0].data.resposta;
                    }

                    if(res[1]){
                        vm.situacoes = res[1].data.resposta[0];
                    }
            });

            if($location.search().status !== undefined){
                vm.filtro = {
                    statusRequerimento: $location.search().status,
                    filtroPrazoAtendimento: {},
                    tipoPesquisaAtendimento: 'RECEBIDOS_NO_PERIODO'
                };
            }

            // Linha comentada para impedir a pesquisa de serviços ao iniciar a tela de Triagem.
            // vm.filterFindAll();
        };

        vm.toUpperCase = function(){
          vm.filtro.nuAtendimento = vm.filtro.nuAtendimento.toUpperCase();
        };

        vm.pausarAtendimento = function(item){
          $mdDialog.show({
            controller: ['$scope', function($scope){

              $scope.requerimento = item;


              $scope.confirmar = function () {
                $mdDialog.hide($scope.requerimento);
              };

              $scope.fechar = function() {
                $mdDialog.cancel();
              };

            }],
            templateUrl: 'scripts-servicos/pages/triagem/view/partials/pausarAtendimento.html',
            parent: angular.element(document.body),
            clickOutsideToClose:true
          }).then(function(item){
            triagemRequerimentoService.pausarAtendimento(item.numeroAtendimento, item.numeroDias, item.justificativa).then(function(){
              $state.reload();
            });
          });
        };

        vm.despausarAtendimento = function(item){
          triagemRequerimentoService.despausarAtendimento(item.numeroAtendimento).then(function(){
            comumServiceServico.mostrarMensagemSucesso("Requerimento continuado com sucesso");
            $state.reload();
          });
        };

        var reiniciaPaginacao = function () {
            vm.registroInicial = 1;
            vm.tamanhoLimite = 5;
        };

        vm.filterFindAll = function() {
            var filtro = angular.copy($scope.filtro);

            if (filtro.colunaOrdenacao === null || !filtro.colunaOrdenacao) {
              mostrarFiltroDefault();
            }

            vm.parametros = filtro;
            reiniciaPaginacao();

            if ($scope.verificarSeTemFiltro(filtro)) {
              pesquisar(filtro);
            } else {
              pesquisar({});
            }
        };

        var pesquisar = function (parametros) {

          triagemRequerimentoService.filterFindAll(parametros, ($scope.registroInicial - 1), $scope.tamanhoLimite)
              .then(function(resp) {
              vm.consultaTriagem = resp.data.resposta[0].content;
              vm.totalItems = resp.data.resposta[0].totalElements;
              vm.formatDate();
          });
        };

        vm.navegarPaginacao = function () {
            var parametros = ($scope.parametros)
                        ? $scope.parametros
                        : {};

            pesquisar(parametros);
        };

        vm.clearFilter = function() {
          vm.filtro = {
            filtroPrazoAtendimento: {},
            tipoPesquisaAtendimento: 'RECEBIDOS_NO_PERIODO'
          };
        };

        vm.verificarSeTemFiltro = function(filtro){
            var result = false;

            angular.forEach(filtro, function(item){
                if(!angular.isUndefined(item != null) && item != ''){
                    result = true;
                }
            });

            return result;
        }

        vm.formatarData = function(datetime){
          if(datetime != null){
            return new Date(datetime).toLocaleDateString();
          }else{
            return null;
          }
        }

        vm.abrirModalAnotacoes = function(item){
            $mdDialog.show({
                controller: ['$scope', function($scope){

                    $scope.requerimento = item;

                    $scope.confirmar = function () {
                        if($scope.requerimento.dataPendenciaVisualizada){
                            $scope.requerimento.dataPendenciaVisualizada
                                        = new Date($scope.requerimento.dataPendenciaVisualizada)
                        }
                        $mdDialog.hide($scope.requerimento);
                    };

                    $scope.fechar = function() {
                        $mdDialog.cancel();
                    };

                }],
                templateUrl: 'scripts-servicos/pages/triagem/view/partials/anotacoes.html',
                parent: angular.element(document.body),
                clickOutsideToClose:true
            }).then(function(item){
               vm.addAnotacoes(item);
            });
        };

        vm.abrirModalCancelarEnvioAutomaticoSei = function(item){
            $mdDialog.show({
                controller: ['$scope','triagemRequerimentoService','ComumFactory','$filter',
                    function($scope,triagemRequerimentoService,ComumFactory,$filter){

                    $scope.requerimento = item;
                    $scope.requerimento.numeroProcesso;
                    $scope.mostraCampoNumero = true;

                    triagemRequerimentoService.getBuscarProcessoNaFila($scope.requerimento.id).then(function(resposta){
                        $scope.filaEnvioSei = resposta.data.resposta[0];
                        if(angular.isDefined($scope.filaEnvioSei.numeroProcedimento)){
                          $scope.requerimento.numeroProcesso = $scope.filaEnvioSei.numeroProcedimento;
                        }
                    });

                    $scope.confirmar = function () {
                        if($scope.mostraCampoNumero){
                            if(angular.isDefined($scope.requerimento.numeroProcesso) && $scope.requerimento.numeroProcesso != null){
                                triagemRequerimentoService.cancelarEnvioAutomaticoSei($scope.requerimento.id, $scope.requerimento.numeroProcesso).then(function(resposta){

                                });
                                $mdDialog.hide($scope.requerimento);
                            }else{
                                ComumFactory.mostrarMensagemError($filter('translate')('Favor, informar o Nº do Processo!'));
                            }
                        }else{
                            triagemRequerimentoService.cancelarEnvioAutomaticoSei($scope.requerimento.id, $scope.requerimento.numeroProcesso).then(function(resposta){
                                $scope.filterFindAll();
                            });
                            $mdDialog.hide($scope.requerimento);
                        }
                    };

                    $scope.fechar = function() {
                        $mdDialog.cancel();
                    };

                }],
                templateUrl: 'scripts-servicos/pages/triagem/view/partials/cancelarEnvioAutomaticoSei.html',
                parent: angular.element(document.body),
                clickOutsideToClose:true
            }).then(function(){
                vm.filterFindAll();
            });
        };

        vm.addAnotacoes = function(item){
            triagemRequerimentoService.addAnotacoes(item).then(function(){
            });
        };

        vm.formatDate = function() {
            angular.forEach($scope.consultaTriagem, function(item){
                if(item.dataPendenciaVisualizada){
                    item.dataPendenciaVisualizada = new Date(item.dataPendenciaVisualizada).toLocaleString();
                }

                //temporário que adiciona um processo fake no requerimento
                var s = parseInt(Math.random() * (9999 - 100) + 1) + "";
                while (s.length < 10) s = "0" + s;
                item.processo = s;
            });
        };

        vm.clearSearchItem = function() {
            vm.searchItem = '';
        };



          init();
    }


})();

(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('tipoProcessoSeiService', ['$http', 'comumServiceServico', '$filter',
      function ($http, comumServiceServico, $filter) {

        var url = '/servicos/api/tipo-processo-sei';
        
        var findById = function (id) {
          return $http.get(url + '/' + id);
        };

        var save = function (tipoProcesso) {
          if (tipoProcesso.id) {
            return $http.put(url, tipoProcesso);
          } else {
            return $http.post(url, tipoProcesso);
          }
        };

        var deletar = function (id) {
          return $http.delete(url + '/' + id);
        };

        var findAll = function (offset, limit) {
          return $http.get(url + '/pesquisar', {
            params: {
              offset: offset,
              limit: limit
            }
          });
        };

        var filterFindAll = function (codigo, descricao, offset, limit) {
          codigo = codigo != undefined ? codigo : null;
          descricao = descricao != undefined ? descricao : null;
                   
          return $http.get(url + '/pesquisar', {
            params: {
              codigo: codigo,
              descricao: descricao,
              offset: offset,
              limit: limit
            }
          });
        };

      
        var listarTiposProcessoSei = function () {
          return $http.get(url);
        };

        var validarMinLength = function (model, msg) {
          var texto = comumServiceServico.htmlToPlaintext(model);

          if (texto.length < 10) {
            comumServiceServico.mostrarMensagemError($filter('translate')('error-min-caracteres', {
              max: '10'
            }));
            return false;
          }

          return true;
        };

        var setarOrdem = function (lista) {
          angular.forEach(lista, function (item, $index) {
            item.ordem = $index;
          });
        };

        var ordenaLista = function (atual, destino, lista) {
          var item = lista[atual];
          lista[atual] = lista[destino];
          lista[destino] = item;
        };

        var verificarExisteCampo = function (mensagens, campo) {
          var existe = false;
          angular.forEach(mensagens, function (item) {
            if (item == campo)
              existe = true;
          });
          return existe;
        };

        var apresentarCamposInvalidos = function (form) {

          var error = form.$error;
          var mensagens = [];
          angular.forEach(error.required, function (field) {
            if (field.$invalid) {
              if (!verificarExisteCampo(mensagens, field.$name))
                mensagens.push(field.$name);
            }
          });

          if (mensagens.length > 0) {
                comumServiceServico.mostrarMensagemError('Necessário preencher os campos obrigatórios');
          }
        };

        var getQueryDefault = function () {
            return angular.copy({
              order: 'tipo',
              limit: 10,
              limitsPage: [5, 10, 20],
              page: 1
            });
        };

        var getQueryNome= function () {
          return angular.copy({
            order: 'nome',
            limit: 10,
            limitsPage: [5, 10, 20],
            page: 1
          });
        };

        return {
          findById: findById,
          save: save,
          deletar: deletar,
          findAll: findAll,
          filterFindAll: filterFindAll,
          listarTiposProcessoSei: listarTiposProcessoSei,
          validarMinLength: validarMinLength,
          setarOrdem: setarOrdem,
          ordenaLista: ordenaLista,
          verificarExisteCampo: verificarExisteCampo,
          apresentarCamposInvalidos: apresentarCamposInvalidos,
          getQueryDefault: getQueryDefault,
          getQueryNome: getQueryNome,
        };
      }])

})();
(function(){

    'use strict';

    angular
        .module('su-servicos')
        .controller('tipoProcessoSeiListController',  ['$scope','tipoProcessoSeiService','$state', '$mdDialog','comumServiceServico',
        function ($scope, tipoProcessoSeiService, $state, $mdDialog,comumServiceServico) {

        var init = function() {
            $scope.tipos = [];
            $scope.minDate = new Date();
            $scope.registroInicial = 1;
            $scope.tamanhoLimite = 5;
            $scope.totalItems = 0;
            $scope.updateTable();

        };

        $scope.updateTable = function(){
            $scope.carregando = true;
            tipoProcessoSeiService.findAll(($scope.registroInicial - 1), $scope.tamanhoLimite).then(function (resp) {
                $scope.tipos = resp.data.resposta[0].content;
                $scope.totalItems = resp.data.resposta[0].totalElements;
            });
        };

        $scope.filterFindAll = function() {
            tipoProcessoSeiService.filterFindAll($scope.codigo, $scope.descricao, ($scope.registroInicial - 1), $scope.tamanhoLimite).then(function(resp) {
                $scope.tipos = resp.data.resposta[0].content;
                $scope.totalItems = resp.data.resposta[0].totalElements;
            });
        };

        $scope.findPagination = function() {
            if ( ($scope.codigo != undefined && $scope.codigo != "")
                 || ($scope.descricao != undefined && $scope.descricao != "")
            ) {
                $scope.filterFindAll();
            } else {
                $scope.updateTable();
            }
        };

        $scope.adicionar = function(e){
          e.preventDefault();
            $state.go('servicos.SERVICOS_CADASTRAR_TIPO_PROCESSO_SEI');
        };

        $scope.clearFilter = function() {
            $scope.codigo = "";
            $scope.descricao = "";
        };

        $scope.showConfirmExcluir = function(ev, id) {
            var confirm = $mdDialog.confirm({focusOnOpen:false})
                .title('Excluir Tipo Processo Sei')
                .textContent('Deseja excluir dados?')
                .targetEvent(ev)
                .ok('Sim')
                .cancel('Não');
            $mdDialog.show(confirm).then(function() {
                tipoProcessoSeiService.deletar(id).then(function(resposta){
                    $scope.updateTable();
                }, function (erro) {

                    var msg = (erro.data.mensagem) ? erro.data.mensagem[0] : erro.data.error;

                    comumServiceServico.mostrarMensagemError(msg);
                });
            }, function() {
                $mdDialog.cancel();
            });
        };

        $scope.novoCadastro = function() {
            $state.go('servicos.SERVICOS_CADASTRAR_TIPO_PROCESSO_SEI');
        };

        init();

    }]);

})();
(function(){

    'use strict';

    angular
        .module('su-servicos')
        .controller('cadastrarTipoProcessoSeiController',
                      ['$scope', '$stateParams', '$state', 'tipoProcessoSeiService', 
                      'comumServiceServico', '$filter', '$element',
            function ($scope, $stateParams, $state, tipoProcessoSeiService, 
                      comumServiceServico, $filter, $element) {

            $element.find('input').on('keydown', function(ev) {
                ev.stopPropagation();
            });

            var vm = $scope;
            vm.isNew = !($stateParams.id);

            var init = function () {

                $scope.searchTerm;
                $scope.clearSearchTerm = function() {
                  $scope.searchTerm = '';
                };

                vm.selected = [];

                if (vm.isNew) {
                    vm.reset();
                    $scope.editando = false;
                } else {

                    vm.tituloStatico = $filter('translate')('label-titulo');

                    tipoProcessoSeiService.findById($stateParams.id).then(function (resposta) {

                        getDados(resposta);

                        $scope.editando = true;

                    });
                }

            };

            var getDados = function (resposta) {
                vm.tipo = resposta.data.resposta[0];

            };

            vm.reset = function () {

                vm.tipo = {
                  descricao: ''
                };

                if (angular.isDefined(vm.manterTipoProcessoSei)) {
                    vm.manterTipoProcessoSei.$setUntouched();
                }
            };

            vm.mostrarResultado = function (box, num_max, campospan) {
              document.getElementById(campospan).innerHTML = box.length + " / 5";
            };

            function indexOf(lista, elemento) {
              for (var i = 0; i < lista.length; i++) {
                if (lista[i].id === elemento.id) {
                   return i;
                }
              }
            }

            vm.validar = function(){
              var valido = vm.form.$valid;
              if (!valido) {
                 servicoService.apresentarCamposInvalidos(vm.form);
              }
              return valido;
            };

            vm.salvar = function () {
              tipoProcessoSeiService.save(vm.tipo).then(function (resposta) {

                    comumServiceServico.mostrarMensagemSucesso('Dados gravados com sucesso');

                    if (!vm.isNew) {
                      getDados(resposta);
                    }

                    $state.go('servicos.SERVICOS_CONSULTAR_TIPO_PROCESSO_SEI');
                    vm.reset();

                }, function (erro) {

                    var msg = (erro.data.mensagem) ? erro.data.mensagem[0] : erro.data.error;

                    comumServiceServico.mostrarMensagemError(msg);
                });
            };

            vm.clearSearchItem = function() {
                vm.searchItem = '';
            };

            init();

        }]);

})();
(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('solicitanteService', ['$resource', function ($resource) {

      var _resources = {
        solicitante: $resource('/servicos/api/solicitante', {}, {
          buscarTodosOrdenado: {method: "GET", isArray: true}
        })
      };

      return {
        get: function ($resource) {
          return _resources.solicitante.buscarTodosOrdenado($resource).$promise;
        }
      };

    }]);

})();

(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('servicoService', ['$http', 'comumServiceServico', '$filter',
      function ($http, comumServiceServico, $filter) {

        var url = '/servicos/api/public/servico';
        var urlBase = '/servicos/api';

        var findById = function (id) {
          return $http.get(url + '/' + id);
        };

        var findAllNiveisAutenticacao = function () {
          return $http.get(urlBase + '/servico/nivelAutenticacao');
        };

        var save = function (servico) {
          if (servico.id) {
            return $http.put(url, servico);
          } else {
            return $http.post(url, servico);
          }
        };

        var deletar = function (id) {
          return $http.delete(url + '/' + id);
        };

        var findAll = function (offset, limit) {
          return $http.get(url, {
            params: {
              offset: offset,
              limit: limit
            }
          });
        };

        var pesquisarTituloServico = function () {
          return $http.get(url + '/pesquisar-titulo-servico');
        };

        var pesquisarTipoDadoComplementar = function(){
            return $http.get(urlBase+  '/dado-complementar/pesquisar-tipos');
        };

         var buscarCombosUtilizadas = function(){
            return $http.get(urlBase+  '/dado-complementar/buscar-combos-utilizadas');
        };

         var buscarArquivoAjuda = function (servicoId) {
           return $http.get(urlBase+  '/public/arquivo-ajuda/buscar-arquivo-ajuda/' + servicoId);
         }

        var apagarArquivoAjuda = function (arquivoAjudaId) {
          return $http.delete(urlBase+  '/public/arquivo-ajuda/apagar-arquivo-ajuda/' + arquivoAjudaId);
        }


        var filterFindAll = function (titulo, situacao, offset, limit) {
          titulo = titulo != undefined ? titulo : "";
          return $http.get(url, {
            params: {
              titulo: titulo,
              situacao: situacao,
              offset: offset,
              limit: limit
            }
          });
        };

        var listarGruposUnidadeSei = function () {
          return $http.get(urlBase + '/grupo-sei/listar');
        };

        var pesquisarPorGrupoUnidadeSei = function (idGrupoUnidadeSei) {
          return $http.get(urlBase + '/servico/pesquisar/grupo-sei/'+idGrupoUnidadeSei);
        };

        var listarTipoDocumento = function () {
          return $http.get(urlBase + '/tipo-documento/buscar-tipo-documento');
        };

        var toggleAtivo = function (servico) {
          return $http.put(url + '/alterarSituacao', servico);
        };

        var validarMinLength = function (model, msg) {
          var texto = comumServiceServico.htmlToPlaintext(model);

          if (texto.length < 10) {
            comumServiceServico.mostrarMensagemError($filter('translate')('error-min-caracteres', {
              max: '10'
            }));
            return false;
          }

          return true;
        };

        var setarOrdem = function (lista) {
          angular.forEach(lista, function (item, $index) {
            item.ordem = $index;
          });
        };


        var ordenaLista = function (atual, destino, lista) {
          var item = lista[atual];
          lista[atual] = lista[destino];
          lista[destino] = item;
        };


        var verificarExisteCampo = function (mensagens, campo) {
          var existe = false;
          angular.forEach(mensagens, function (item) {
            if (item == campo)
              existe = true;
          });
          return existe;
        };


        var apresentarCamposInvalidos = function (form) {

          var error = form.$error;
          var mensagens = [];
          angular.forEach(error.required, function (field) {
            if (field.$invalid) {
              if (!verificarExisteCampo(mensagens, field.$name))
                mensagens.push(field.$name);
            }
          });

          if (mensagens.length > 0) {
                comumServiceServico.mostrarMensagemError('Necessário preencher os campos obrigatórios');
          }
        };


        var formatarPalavrasChaves = function (servico, palavrasChaves) {
          servico.palavrasChaves = [];
          if (palavrasChaves.length == 0) {
            var msg = 'É necessário informar uma palavra chave.';
            comumServiceServico.mostrarMensagemError(msg);
            throw msg;
          }
          angular.forEach(palavrasChaves, function (palavra) {
            servico.palavrasChaves.push({descricao: palavra});
          });
        };

        var formatarPalavrasChavesSalvas = function (servico, callback) {
          if (servico && servico.palavrasChaves) {
            var palavrasChaves = [];
            angular.forEach(servico.palavrasChaves, function (palavra) {
              palavrasChaves.push(palavra.descricao);
            });
            callback(palavrasChaves);
          }
        };

        var getQueryDefault = function () {
            return angular.copy({
              order: 'tipo',
              limit: 10,
              limitsPage: [5, 10, 20],
              page: 1
            });
        };

        var getQueryNome= function () {
          return angular.copy({
            order: 'nome',
            limit: 10,
            limitsPage: [5, 10, 20],
            page: 1
          });
        };

        var getTiposComponentes = function () {
          return $http.get(url + '/tiposComponentes');
        };

        return {
          findById: findById,
          save: save,
          findAllNiveisAutenticacao: findAllNiveisAutenticacao,
          deletar: deletar,
          findAll: findAll,
          filterFindAll: filterFindAll,
          toggleAtivo: toggleAtivo,
          listarGruposUnidadeSei: listarGruposUnidadeSei,
          pesquisarPorGrupoUnidadeSei: pesquisarPorGrupoUnidadeSei,
          listarTipoDocumento: listarTipoDocumento,
          pesquisarTituloServico: pesquisarTituloServico,
          validarMinLength: validarMinLength,
          setarOrdem: setarOrdem,
          ordenaLista: ordenaLista,
          verificarExisteCampo: verificarExisteCampo,
          apresentarCamposInvalidos: apresentarCamposInvalidos,
          formatarPalavrasChaves: formatarPalavrasChaves,
          formatarPalavrasChavesSalvas: formatarPalavrasChavesSalvas,
          getQueryDefault: getQueryDefault,
          getQueryNome: getQueryNome,
          pesquisarTipoDadoComplementar: pesquisarTipoDadoComplementar,
          buscarCombosUtilizadas: buscarCombosUtilizadas,
          buscarArquivoAjuda: buscarArquivoAjuda,
          apagarArquivoAjuda: apagarArquivoAjuda,
          getTiposComponentes: getTiposComponentes
        };
      }])

})();

(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('modalSubsecoesFactory', ['$mdDialog', 'servicoService', 'comumServiceServico', '$filter', 'cloneUtils',
      function ($mdDialog, servicoService, comumServiceServico, $filter, comum, cloneUtils) {

        var SERVICO_GENERICO_COM_RIP = 'servicoGenericoComRip';
        var SERVICO_GENERICO_SEM_RIP = 'servicoGenericoSemRip';


        var isSubsecao = function(formularioWeb){
          return (formularioWeb.rota == SERVICO_GENERICO_COM_RIP
            || formularioWeb.rota == SERVICO_GENERICO_SEM_RIP);
        };

        var abrirModalSubsecoes = function(ev, dadosComplementares, subsecoes, servico, arquivoEdicao, callback){

          $mdDialog.show({
            controller: ['$scope', 'cloneUtils', function ($scope, cloneUtils) {

              var init = function(){

                $scope.subsecoes = subsecoes || [];
                $scope.dadosComplementares = dadosComplementares || [];
                exibeDado();

                $scope.subsecao = {
                  nome: '',
                  campos: [],
                  status: true
                };

                $scope.limpar();
                $scope.indexEditar = undefined;
                $scope.editar = false;

                if(angular.isDefined(arquivoEdicao)){
                  $scope.editarDados(arquivoEdicao);
                }
              };

              $scope.limpar = function(){
                $scope.dadoComplementar = undefined;
              };

              $scope.editarDados = function(subsecao){
                $scope.editar = true;
                angular.forEach($scope.subsecoes, function(item, $index){
                  if(item === subsecao){
                    $scope.subsecao = angular.copy(item);
                    $scope.indexEditar = $index;
                  }
                });
              };

              var exibeDado = function(){
                $scope.dadosComplementares.forEach(function(dado, $index){
                  dado.hasSubsecao = false;
                  dado.index = $index;
                });
                $scope.subsecoes.forEach(function(subsecao){
                  subsecao.campos.forEach(function(campo){
                    $scope.dadosComplementares.forEach(function(dado){
                      if(campo == dado){
                        dado.hasSubsecao = true;
                      }
                    });
                  });
                });
              };

              $scope.removerCampo = function (event, campo) {
                var index = $scope.subsecao.campos.indexOf(campo);
                $scope.dadosComplementares[campo.index].hasSubsecao = false;
                $scope.subsecao.campos.splice(index, 1);
              };

              $scope.closeDialog = function () {
                $mdDialog.cancel();
              };

              $scope.adicionar = function(){
                if($scope.formSubsecoes.$valid){

                  $scope.subsecao.campos
                      .push($scope.dadoComplementar);
                  $scope.dadoComplementar.hasSubsecao = true;

                  $scope.limpar();

                }else{
                  comumServiceServico
                    .mostrarMensagemError('Necessário preencher os campos obrigatórios');
                }
              };


              $scope.confirmar = function(){
                if($scope.editar){
                  cloneUtils.clone($scope.subsecoes[$scope.indexEditar], $scope.subsecao);
                  if(!$scope.subsecoes[$scope.indexEditar].campos){
                    $scope.subsecoes[$scope.indexEditar].campos = [];
                  }
                  for(var i = 0; i < $scope.subsecao.campos.length; i++){
                    $scope.subsecoes[$scope.indexEditar].campos[i] = campoRelativo($scope.subsecao.campos[i]);
                  }
                } else {
                  $scope.subsecoes.push($scope.subsecao);
                }
                callback($scope.subsecoes);
                $scope.closeDialog();
              };

              var campoRelativo = function(campo){
                for(var i = 0; i < $scope.dadosComplementares.length; i++){
                  if(campo.index === $scope.dadosComplementares[i].index){
                    return $scope.dadosComplementares[i];
                  }
                }
                return undefined;
              };

              init();
            }],
            templateUrl: 'scripts-servicos/pages/servicos/view/partials/subsecoes.html',
            parent: angular.element(document.body),
            targetEvent: ev,
            clickOutsideToClose: false,
            locals: {
            }
          }).then(function () {

          });
        };

        var init = function(){
        };

        init();

        return {
          isSubsecao: isSubsecao,
          abrirModalSubsecoes: abrirModalSubsecoes
        };

      }]);
})();

(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('modalSolicitanteFactory', ['$mdDialog', 'comumServiceServico', '$filter',
      function ($mdDialog, comumServiceServico, $filter) {


      var cancelarAssociadosAoSolicitante = function(lista, solicitante, model){

        for(var j=0; j < lista.length; j++){

          if(!lista[j].solicitantes){
            continue;
          }

          for(var i=0; i<lista[j].solicitantes.length; i++){
            if(solicitante.id == lista[j].solicitantes[i].id){
              var item = lista[j];
              var index = model.indexOf(item)
              model[index].status = false;
              return;
            }
          }
        }
      };

      var abrirModalRemoverSolicitante = function (ev, item, listas, servico) {
          var confirm = $mdDialog.confirm({focusOnOpen: false})
            .title($filter('translate')('solicitante'))
            .htmlContent('Deseja confirmar exclusão?<br /><br />(Os documentos e declarações deste solicitante também serão removidos)')
            .targetEvent(ev)
            .ok($filter('translate')('sim'))
            .cancel($filter('translate')('nao'));

          $mdDialog.show(confirm).then(function () {

            var index = listas.solicitante.indexOf(item);

            if (listas.solicitante[index].id) {

              var indexServico = servico.solicitantes.indexOf(item);
              servico.solicitantes[indexServico].status = false;

              cancelarAssociadosAoSolicitante(
                listas.documento,
                servico.solicitantes[indexServico],
                servico.documentos);

              cancelarAssociadosAoSolicitante(
                listas.declaracao,
                servico.solicitantes[indexServico],
                servico.declaracoes);
            }

            // cancelarItemDaListaAssociadoAoSolicitante(item, listas.documento);
            // cancelarItemDaListaAssociadoAoSolicitante(item, listas.declaracao);

            listas.solicitante.splice(index, 1);

          }, function () {
            $mdDialog.cancel();
          });
        };




      var validarDuplicidade = function (isEdicao, solicitantes, novoSolicitante, itemOld) {

        if(isEdicao){
          if (novoSolicitante.tipo == itemOld.tipo
            && novoSolicitante.requisitos == itemOld.requisitos) {
            return false;
          }
        }

        for (var i = 0; i < solicitantes.length; i++) {

          if (solicitantes[i].tipo == novoSolicitante.tipo
            && solicitantes[i].requisitos == novoSolicitante.requisitos) {

            comumServiceServico.mostrarMensagemError($filter('translate')('erro-campo-duplicado', {
              campo: 'Solicitante'
            }));
            return true;
          }
        }

        return false;
      };


        var editarSolicitante = function(listas, solicitantes, novoSolicitante, itemOld){

          /* editar solicitante */
          for(var i=0; i<solicitantes.length;i++){
            if (solicitantes[i].tipo == itemOld.tipo
              && solicitantes[i].requisitos == itemOld.requisitos) {

              solicitantes[i] = angular.copy(novoSolicitante);
            }
          }

          /* editar documentos */
          for(var i=0; i<listas.documento.length; i++){
            for(var j=0; j<listas.documento[i].solicitantes.length;j++){

              if (listas.documento[i].solicitantes[j].tipo == itemOld.tipo
                && listas.documento[i].solicitantes[j].requisitos == itemOld.requisitos) {

                listas.documento[i].solicitantes[j] = angular.copy(novoSolicitante);
              }
            }
          }

          /* editar declaracoes */
          for(var i=0; i<listas.declaracao.length; i++){
            for(var j=0; j<listas.declaracao[i].solicitantes.length;j++){

              if (listas.declaracao[i].solicitantes[j].tipo == itemOld.tipo) {

                listas.declaracao[i].solicitantes[j] = angular.copy(novoSolicitante);
              }
            }

          }
        };

        var adicionar = function ($event, form, listas, solicitantes,
                                     novoSolicitante, itemOld, isEdicao, callback) {

          if (!novoSolicitante.tipo || novoSolicitante.tipo.trim().length == 0) {
            comumServiceServico.mostrarMensagemError(
                    $filter('translate')('error-informe-tipo'));
            return;
          }

          if (!novoSolicitante.requisitos || form.$error.minlength) {

            if(form.$error.maxlength) {
              comumServiceServico.mostrarMensagemError($filter('translate')('error-max-campo', {
                max: 300,
                campo: 'Requisitos'
              }));
            }else{
              comumServiceServico.mostrarMensagemError($filter('translate')('error-min-campo', {
                min: 10,
                campo: 'Requisitos'
              }));
            }

            return;
          }

          $event.preventDefault();

          if (validarDuplicidade(isEdicao, solicitantes, novoSolicitante, itemOld)) {
            return;
          }

          if (isEdicao) {

            editarSolicitante(listas, solicitantes, novoSolicitante, itemOld);

          } else {

            novoSolicitante.status = true;
            solicitantes.push(novoSolicitante);
          }


          callback(false, undefined);

        };


        var remover = function ($event, servico, listas, solicitantes, item) {

          var index = solicitantes.indexOf(item);

          if (solicitantes[index].id) {
            for (var i = 0; i < servico.solicitantes.length; i++) {
              if (servico.solicitantes[i].id == solicitantes[index].id) {
                servico.solicitantes[i].status = false;

                cancelarAssociadosAoSolicitante(
                  listas.documento,
                  servico.solicitantes[i],
                  servico.documentos);

                cancelarAssociadosAoSolicitante(
                  listas.declaracao,
                  servico.solicitantes[i],
                  servico.declaracoes);

              }
            }
          }

          solicitantes.splice(index, 1);
        };



      return {
        validarDuplicidade: validarDuplicidade,
        editarSolicitante: editarSolicitante,
        adicionar: adicionar,
        remover: remover,
        abrirModalRemoverSolicitante: abrirModalRemoverSolicitante
      };


    }]);

})();

(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('modalDocumentosFactory', ['$mdDialog', 'comumServiceServico', '$filter',
      function ($mdDialog, comumServiceServico, $filter) {


        var getFormatoArquivoSelecionado = function (novoDocumento, listaFormatosArquivos) {
          novoDocumento.formatoArquivos = [];

          angular.forEach(listaFormatosArquivos, function (formatoDoc) {
            if (formatoDoc.selecionado) {
              novoDocumento.formatoArquivos.push(formatoDoc);
            }
          });
        };


        var limparFormatosSelecionados = function (listaFormatosArquivos, callback) {
          angular.forEach(listaFormatosArquivos, function (doc) {
            doc.selecionado = false;
          });

          callback();
        };


        var validarSolicitantes = function(documentoAtual, novoDocumento){

          if(documentoAtual.solicitantes.length
            != novoDocumento.solicitantes.length){
            return false;
          }

          for(var i=0; i<documentoAtual.solicitantes.length;i++){

            var exists = false;

            for(var j=0; j<novoDocumento.solicitantes.length; j++){
              if(documentoAtual.solicitantes[i].tipo
                == novoDocumento.solicitantes[j].tipo){
                exists = true;
              }
            }

            if(!exists){
              return false;
            }
          }

          return true;
        };


        var validarDuplicidade = function (documentos, novoDocumento, itemOld, isEdicao) {

          var solicitanteValido = !(isEdicao
                                      && novoDocumento.nome == itemOld.nome
                                      && validarSolicitantes(novoDocumento, itemOld));

          for (var i = 0; i < documentos.length && solicitanteValido; i++) {

            if (documentos[i].nome == novoDocumento.nome
                      && validarSolicitantes(documentos[i], novoDocumento)) {

              comumServiceServico.mostrarMensagemError($filter('translate')('erro-campo-duplicado', {
                campo: 'Documento'
              }));

              return true;
            }
          }

          return false;
        };


        var findExtensao = function (arrayFormatos, extensao) {
          for (var i = 0; i < arrayFormatos.length; i++) {
            if (extensao == arrayFormatos[i].formato)
              return true;
          }
          return false;
        };


        var modificarIndiceSolicitante = function (novoDocumento) {
          var i = 0;
          angular.forEach(novoDocumento.solicitantes, function (solicitante) {
            solicitante.indice = i;
            i++;
          });
        };


        var selecionarCheckboxsFormatosArq = function (novoDocumento,listaFormatosArquivos ) {
          angular.forEach(novoDocumento.formatoArquivos, function (formatoSelecionado) {
            angular.forEach(listaFormatosArquivos, function (formatoArray) {
              if (formatoSelecionado.id == formatoArray.id) {
                formatoArray.selecionado = true;
              }
            });
          });
        };

        var addDocumento = function ($event, form, documentos, novoDocumento,
                                     statusExtensaoDocumento, outraExtensaoDocumento,
                                     listaFormatosArquivos, itemOld, isEdicao, callback) {

          form.$submitted = true;
          var extensao = '';

          getFormatoArquivoSelecionado(novoDocumento, listaFormatosArquivos);

          if (!novoDocumento.nome || novoDocumento.nome.trim() == "") {
            comumServiceServico.mostrarMensagemError('É necessário informar o campo de Nome do Documento.');
            return;
          }
          if (!novoDocumento.tipo || novoDocumento.tipo.trim() == "") {
            comumServiceServico.mostrarMensagemError('É necessário informar o campo Situação.');
            return;
          }
          if (!novoDocumento.solicitantes || novoDocumento.solicitantes.length == 0) {
            comumServiceServico.mostrarMensagemError('É necessário informar o campo de Solicitante.');
            return;
          }
          if (!novoDocumento.tipoDocumento || !novoDocumento.tipoDocumento.id) {
            comumServiceServico.mostrarMensagemError('É necessário informar o campo de Tipo de Documento.');
            return;
          }
          if ((!novoDocumento.formatoArquivos || novoDocumento.formatoArquivos.length == 0)
                  && (!statusExtensaoDocumento || outraExtensaoDocumento == "")) {
            comumServiceServico.mostrarMensagemError('É necessário informar pelo menos uma Extensão de Documento.');
            return;
          }
          if (statusExtensaoDocumento && outraExtensaoDocumento != "") {
            extensao = outraExtensaoDocumento.replace(/[\W]+/g, "").toUpperCase();

            var resp = findExtensao(listaFormatosArquivos, extensao);


            if (resp) {
              comumServiceServico.mostrarMensagemError('O formato de extensão informado já existe.');
              return;
            } else {
              novoDocumento.formatoArquivos.push({
                'formato': extensao
              });
            }
          }

          $event.preventDefault();


          if (validarDuplicidade(documentos, novoDocumento, itemOld, isEdicao)) {
            return;
          }

          if (isEdicao) {

            for(var i=0; i<documentos.length;i++){

              if (documentos[i].tipo == itemOld.tipo
                && documentos[i].nome == itemOld.nome) {

                documentos[i] = angular.copy(novoDocumento);
              }
            }

          } else {
            novoDocumento.status = true;
            documentos.push(novoDocumento);
          }


          callback();

        };


        var setarSolicitantes = function (solicitantes) {
          var solicitanteEditado = [];
          var i = 0;
          angular.forEach(solicitantes, function (solicitante) {
            if (!solicitante.indice) {
              solicitante.indice = i++;
            }
            solicitanteEditado.push(solicitante);
          });
          return solicitanteEditado;
        };


        var remover = function ($event,documentos, servico, item) {

          var index = documentos.indexOf(item);

          for (var i = 0; i < servico.documentos.length && documentos[index].id; i++) {
            if (servico.documentos[i].id ==  documentos[index].id) {
                 servico.documentos[i].status = false;
            }
          }

          documentos.splice(index, 1);
        };


        return {
          getFormatoArquivoSelecionado: getFormatoArquivoSelecionado,
          limparFormatosSelecionados: limparFormatosSelecionados,
          validarSolicitantes: validarSolicitantes,
          validarDuplicidade: validarDuplicidade,
          addDocumento: addDocumento,
          modificarIndiceSolicitante: modificarIndiceSolicitante,
          selecionarCheckboxsFormatosArq: selecionarCheckboxsFormatosArq,
          setarSolicitantes: setarSolicitantes,
          remover: remover
        };


      }]);

})();

(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('modalDeclaracaoFactory', ['$mdDialog', 'comumServiceServico', '$filter', 'FormularioFactory',
      'servicoService',
      function ($mdDialog, comumServiceServico, $filter, FormularioFactory, servicoService) {

        var validarDuplicidade = function (declaracoes, novoItemDeclaracao) {
          for (var i = 0; i < declaracoes.length; i++) {

            var textoLista = comumServiceServico
              .htmlToPlaintext(declaracoes[i].item);

            var textoNovo = comumServiceServico
              .htmlToPlaintext(novoItemDeclaracao.item);

            if (textoLista === textoNovo) {
              return true;
            }
          }
          return false;
        };

        var validar = function(form, novoItemDeclaracao, declaracoes){

          if(!FormularioFactory.validarFormularioComMensagem(
              form, $filter('translate')('error-informe-solicitante'))){
              return false;
          }

          if(!servicoService.validarMinLength(novoItemDeclaracao.item)){
            return false;
          }

          if(validarDuplicidade(declaracoes, novoItemDeclaracao)){
            comumServiceServico.mostrarMensagemError($filter('translate')('erro-campo-duplicado', {
              campo: 'Declaração'
            }));
            return false;
          }

          return true;
        };


        return {
          validar: validar
        };


      }]);

})();

(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('modalDadosComplementaresFactory', ['$mdDialog', 'servicoService', 'comumServiceServico',
            'arquivoServiceServicos', '$filter', 'cloneUtils',
             function ($mdDialog, servicoService, comumServiceServico, arquivoServiceServicos, $filter, comum, cloneUtils) {

        var SERVICO_GENERICO_COM_RIP = 'servicoGenericoComRip';
        var SERVICO_GENERICO_SEM_RIP = 'servicoGenericoSemRip';

        var ID_TEXTO = 1;
        var ID_NUMERO_DECIMAL = 2;
        var ID_NUMERO_INTEIRO = 3;
        var ID_COMBO = 5;
        var ID_COMBO_UF = 11;
        var ID_EMAIL = 13;
        var ID_COMPONENTE = 6;
        var ID_TIPO_UF_ATENDIMENTO = 10;

        var tipoDadosComplementar = [];

        var NOVA_COMBO = 'Nova Combo';
        var CARTORIO = 'Cartório';
        var ENDERECO = 'Endereço';

        var NOME_CAMPO_UF_ATENDIMENTO = 'UF de Localização do Imóvel';

        var SIM = 'Sim';
        var NAO = 'Não';

        var isDadosComplementar = function(formularioWeb){
            return (formularioWeb.rota == SERVICO_GENERICO_COM_RIP
                            || formularioWeb.rota == SERVICO_GENERICO_SEM_RIP);
        };

        var buscarTipos = function(){
            servicoService.pesquisarTipoDadoComplementar()
                .then(function(resposta){
                 tipoDadosComplementar = resposta.data.resposta;
            });
        };

        var buscarCombosUtilizadas = function(callback){
            servicoService.buscarCombosUtilizadas()
                .then(function(resposta){
                    callback(resposta.data.resposta);
            });
        };

        var abrirModalDadosComplementares = function(ev, lista, servico, dadoEdicao, callback){

            $mdDialog.show({
                controller: ['$scope', 'cloneUtils', function ($scope, cloneUtils) {

                    var init = function(){

                        $scope.isNumeroInteiro = comumServiceServico.isNumeroInteiro;
                        $scope.addDocumentos = arquivoServiceServicos.addArquivoSemValidar;
                        $scope.queryDados = servicoService.getQueryDefault();

                        lista.forEach(function(item, $index){
                          item.removed = false;
                        });

                        $scope.listaDadosComplementar = angular.copy(lista || []);
                        $scope.valoresObrigatoriedade = [SIM, NAO];

                        $scope.tipoDadosComplementar = tipoDadosComplementar;
                        $scope.limpar();

                        criaListaCombos();

                        if(angular.isDefined(dadoEdicao)){
                            $scope.editarDados(dadoEdicao);
                            $scope.possuiDependencia = !angular.isUndefined($scope.dadoComplementar) &&
                                                       $scope.dadoComplementar.campoDependente != null;
                        }
                    };

                    var criaListaCombos = function(){
                      var lista = [];
                      $scope.listaDadosComplementar.forEach(function(dadoComplementar){
                        if(dadoComplementar.tipoDadoComplementar.id == ID_COMBO){
                          lista.push(dadoComplementar);
                        }
                      });
                      $scope.listaCombos = lista;
                    };

                     var getValueComboOuComponente = function(){

                        if($scope.isCombo()){
                            buscarCombosUtilizadas(function(combos){
                                $scope.valoresCompoComponentes = [{'nomeCampo': NOVA_COMBO}];
                                if(combos.length > 0){
                                    angular.forEach(combos, function(item){
                                         $scope.valoresCompoComponentes.push(item);
                                    });
                                }
                            });
                        }else if($scope.isComponente()){

                          // triagemRequerimentoService.getTiposComponentes().then(function (resposta) {
                          //   $scope.valoresCompoComponentes = resposta.data.resposta;
                          // });
                            $scope.valoresCompoComponentes = [{'nomeCampo': CARTORIO}, {'nomeCampo': ENDERECO}];
                        }
                    };

                    var preencherDados = function(){
                        $scope.addChips();

                        $scope.dadoComplementar.obrigatorio = ($scope.dadoComplementar.obrigatoriedade == SIM);

                        if($scope.dadoComplementar.tamanhoCampo && parseInt($scope.dadoComplementar.tamanhoCampo) > 0){
                                $scope.dadoComplementar.tamanhoCampo = comumServiceServico
                                    .arrendondarDuasCasasDecimais($scope.dadoComplementar.tamanhoCampo);
                        }

                        if($scope.dadoComplementar.comboCompoenenteSelecionando){
                            $scope.dadoComplementar.comboComponente
                                        = $scope.dadoComplementar.comboCompoenenteSelecionando.nomeCampo;
                        }
                    };

                    $scope.removeObrigatoriedade = function(){
                      $scope.dadoComplementar.obrigatoriedade = false;
                      $scope.dadoComplementar.campoDependente = undefined;
                      $scope.dadoComplementar.valorDependente = undefined;
                    };


                  $scope.subirPosicaoDadoComplementar = function (index) {
                    if (index > 0) {
                      var posicaoTemporaria = $scope.listaDadosComplementar[index - 1];
                      $scope.listaDadosComplementar[index - 1] = $scope.listaDadosComplementar[index];
                      $scope.listaDadosComplementar[index] = posicaoTemporaria;
                      if(index == $scope.indexEditar){
                        $scope.indexEditar--;
                      }
                    }
                  };

                  $scope.descerPosicaoDadoComplementar = function (index) {
                    var tamanho = $scope.listaDadosComplementar.length;
                    if (index < (tamanho - 1)) {
                      var posicaoTemporaria = $scope.listaDadosComplementar[index + 1];
                      $scope.listaDadosComplementar[index + 1] = $scope.listaDadosComplementar[index];
                      $scope.listaDadosComplementar[index] = posicaoTemporaria;
                      if(index == $scope.indexEditar){
                        $scope.indexEditar++;
                      }
                    }
                  };

                  $scope.limpar = function(){
                        $scope.dadoComplementar = {
                            valorComboDadoComplementar: [],
                            arquivosAjuda: [],
                            status: true
                        };
                        $scope.valoresCompoComponentes = [];
                        $scope.obrigatoriedade  = undefined;
                        $scope.indexEditar  = undefined;
                        $scope.editar = false;
                        $scope.listaChips = [];
                    };

                    $scope.isCombo = function(){
                        return angular.isDefined($scope.dadoComplementar.tipoDadoComplementar)
                            && $scope.dadoComplementar.tipoDadoComplementar.id == ID_COMBO;
                    };

                    $scope.isComponente = function(tipo){
                        return angular.isDefined($scope.dadoComplementar.tipoDadoComplementar)
                            && $scope.dadoComplementar.tipoDadoComplementar.id == ID_COMPONENTE;
                    };

                    $scope.isNovaCombo = function(){
                        return angular.isDefined($scope.dadoComplementar.comboCompoenenteSelecionando)
                            && $scope.dadoComplementar.comboCompoenenteSelecionando.nomeCampo == NOVA_COMBO;
                    };

                  $scope.isComboUf = function () {
                    return angular.isDefined($scope.dadoComplementar.tipoDadoComplementar)
                      && $scope.dadoComplementar.tipoDadoComplementar.id == ID_COMBO_UF;
                  };

                  $scope.isEmail = function () {
                    return angular.isDefined($scope.dadoComplementar.tipoDadoComplementar)
                      && $scope.dadoComplementar.tipoDadoComplementar.id == ID_EMAIL;
                  };


                    $scope.alterarTipoCampo = function(){
                        if(!$scope.editar){
                            $scope.dadoComplementar.tamanhoCampo = undefined;
                            $scope.dadoComplementar.comboCompoenenteSelecionando = undefined;

                            $scope.dadoComplementar.comboComponente = undefined;
                            $scope.listaChips = [];
                        }


                        if($scope.isComponente()){
                            $scope.dadoComplementar.nomeCampo = undefined;
                        }

                        getValueComboOuComponente();
                    };

                    $scope.isNumericoOuTexto = function(){
                        return angular.isDefined($scope.dadoComplementar.tipoDadoComplementar)
                            && [ID_TEXTO, ID_NUMERO_INTEIRO, ID_NUMERO_DECIMAL]
                                    .indexOf($scope.dadoComplementar.tipoDadoComplementar.id ) != -1;
                    };

                    $scope.isNumeroDecimal = function () {
                      return angular.isDefined($scope.dadoComplementar.tipoDadoComplementar)
                          && [ID_NUMERO_DECIMAL].indexOf($scope.dadoComplementar.tipoDadoComplementar.id) != -1;
                    };

                    $scope.removerArquivo = function (event, arquivo) {
                        var index = $scope.dadoComplementar.arquivosAjuda.indexOf(arquivo);
                        $scope.dadoComplementar.arquivosAjuda.splice(index, 1);
                    };

                    $scope.removerDados = function (dadoComplementar) {
                         var index = $scope.listaDadosComplementar.indexOf(dadoComplementar);
                         for(var i=0; i < servico.campoDadoComplementar.length && $scope.listaDadosComplementar[index].id ;i++){
                            if (servico.campoDadoComplementar[i].id ==  dadoComplementar.id) {
                                servico.campoDadoComplementar[i].status = false;
                            }
                        }
                        lista[index].removed = true;
                        $scope.listaDadosComplementar.splice(index, 1);
                    };

                    $scope.closeDialog = function () {
                        $mdDialog.cancel();
                    };

                    $scope.editarDados = function(dadoComplementar){
                        $scope.editar = true;
                        angular.forEach($scope.listaDadosComplementar, function(item, $index){
                            if(item.nomeCampo == dadoComplementar.nomeCampo){
                                $scope.dadoComplementar = angular.copy(item);
                                $scope.indexEditar = $index;
                            }
                        });

                        if($scope.isComponente() || $scope.isCombo()){
                            $scope.dadoComplementar.comboCompoenenteSelecionando = {
                                nomeCampo: angular.copy($scope.dadoComplementar.comboComponente)
                            };
                        }

                        getValueComboOuComponente();
                        $scope.selecionarCombo();
                    };

                    $scope.selecionarCombo = function(){

                        if($scope.isCombo()){
                            $scope.listaChips = [];
                            if($scope.dadoComplementar.comboComponente
                                        == $scope.dadoComplementar.comboCompoenenteSelecionando.nomeCampo){
                               $scope.dadoComplementar
                                    .comboCompoenenteSelecionando.valorComboDadoComplementar = angular.copy($scope.dadoComplementar.valorComboDadoComplementar);
                            }

                            angular.forEach($scope.dadoComplementar.comboCompoenenteSelecionando.valorComboDadoComplementar, function(valor){
                                $scope.listaChips.push(valor.descricao);
                            });

                        }else if($scope.isComponente()){
                            $scope.dadoComplementar.nomeCampo
                                 = angular.copy($scope.dadoComplementar.comboCompoenenteSelecionando.nomeCampo);

                          switch($scope.dadoComplementar.comboCompoenenteSelecionando.nomeCampo) {
                            case 'Cartório':
                              $scope.dadoComplementar.tipoComponente = "CARTORIO";
                              break;
                            case 'Endereço':
                              $scope.dadoComplementar.tipoComponente = "ENDERECO";
                              break;
                            default:
                              $scope.dadoComplementar.tipoComponente = null;
                          }

                        } else if($scope.isComboUf()){
                          $scope.listaChips = [];
                          angular.forEach($scope.dadoComplementar.comboCompoenenteSelecionando, function(valor){
                            $scope.listaChips.push(valor);
                          });
                        }
                    };


                    $scope.adicionar = function(){

                        if($scope.formDadosComplementares.$valid){

                            preencherDados();

                            if($scope.editar){
                              cloneUtils.clone($scope.listaDadosComplementar[$scope.indexEditar], $scope.dadoComplementar);
                            }else{
                                $scope.listaDadosComplementar
                                        .push($scope.dadoComplementar);
                            }

                            $scope.limpar();
                            comumServiceServico.resertarFormulario($scope, 'formDadosComplementares');
                            criaListaCombos();

                        }else{
                            comumServiceServico
                                    .mostrarMensagemError('Necessário preencher os campos obrigatórios');
                        }
                    };

                    $scope.addChips = function(){
                        $scope.dadoComplementar.valorComboDadoComplementar = [];

                        angular.forEach($scope.listaChips, function(valor){
                                $scope.dadoComplementar
                                    .valorComboDadoComplementar.push({'descricao': valor});
                        });
                    };

                    $scope.removeChips = function(palavra){
                        for(var i=0; i<$scope.dadoComplementar.valorComboDadoComplementar.length; i++){
                            if($scope.dadoComplementar.valorComboDadoComplementar.descricao == palavra){
                                $scope.dadoComplementar.valorComboDadoComplementar.splice(i, 1);
                            }
                        }
                    };

                    $scope.confirmar = function(){
                      lista.forEach(function(item, $index){
                        if(item.removed){
                          lista.splice($index, 1);
                        }
                      });
                      cloneUtils.cloneList(lista, $scope.listaDadosComplementar);
                        callback($scope.listaDadosComplementar);
                        $scope.closeDialog();
                    };

                  comumServiceServico.listarTodasUfs().then(function (resposta) {
                    $scope.listUfs = resposta.data.resposta;
                  });

                    init();
                }],
                templateUrl: 'scripts-servicos/pages/servicos/view/partials/dadosComplementares.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose: false,
                locals: {
                }
            }).then(function () {

            });
        };


        var adicionarNovaUfAtendimento = function(servico){
            var lista = [{
                nomeCampo: NOME_CAMPO_UF_ATENDIMENTO,
                obrigatorio: true,
                status: true,
                ordem: 0,
                tipoDadoComplementar: {
                    id : ID_TIPO_UF_ATENDIMENTO
                }
            }];

            var valido = true;

            angular.forEach(servico.campoDadoComplementar, function (dados) {
                var item = angular.copy(dados);

                if(item.tipoDadoComplementar.id == ID_TIPO_UF_ATENDIMENTO){
                    if(!item.status){
                        dados.status = true;
                    }
                    valido = false;
                    return;
                }

                item.ordem += 1;
                lista.push(item);
            });

            if(valido){
                servico.campoDadoComplementar = lista;
            }
        };


        var editarUfAtendimento = function(servico){
            angular.forEach(servico.campoDadoComplementar, function (dados) {
                var item = angular.copy(dados);

                if(item.tipoDadoComplementar.id == ID_TIPO_UF_ATENDIMENTO){
                    dados.status = false;
                    return;
                }
            });
        };


         var adicionarServicoGenericoUFAtendimento = function(servico){

            if (servico.formularioWeb != undefined) {
             if (servico.formularioWeb.rota == SERVICO_GENERICO_SEM_RIP) {
               if (servico.retirarDadosImovel) {
                 adicionarNovaUfAtendimento(servico);
               } else if (servico.id) {
                 editarUfAtendimento(servico);
               }
             }
           }
         };


        var isCampoUFAtendimento = function(campo){
            return campo.nomeCampo == NOME_CAMPO_UF_ATENDIMENTO;
        };

        var init = function(){
            buscarTipos();
        };

        init();


        return {
            isDadosComplementar: isDadosComplementar,
            abrirModalDadosComplementares: abrirModalDadosComplementares,
            adicionarServicoGenericoUFAtendimento: adicionarServicoGenericoUFAtendimento,
            isCampoUFAtendimento: isCampoUFAtendimento
        };

      }]);
})();

(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('modalArquivosAjudaFactory', ['$mdDialog', 'servicoService', 'comumServiceServico',
            'arquivoServiceServicos', '$filter',
             function ($mdDialog, servicoService, comumServiceServico, arquivoServiceServicos, $filter, comum) {

        var SERVICO_GENERICO_COM_RIP = 'servicoGenericoComRip';
        var SERVICO_GENERICO_SEM_RIP = 'servicoGenericoSemRip';
  


        var isArquivosAjuda = function(formularioWeb){
            return (formularioWeb.rota == SERVICO_GENERICO_COM_RIP
                            || formularioWeb.rota == SERVICO_GENERICO_SEM_RIP);
        };

         var abrirModalArquivosAjuda = function(ev, lista, servico, arquivoEdicao, callback){

           $mdDialog.show({
             controller: ['$scope', function ($scope) {

               var init = function(){

                 $scope.addDocumentos = arquivoServiceServicos.addArquivoAjuda;
                 $scope.listaArquivosAjuda = angular.copy(lista || []);
                 $scope.limpar();

                 if(angular.isDefined(arquivoEdicao)){
                   $scope.editarDados(arquivoEdicao);
                 }
               };

               $scope.subirPosicao = function (index) {
                 if (index > 0) {
                   var posicaoTemporaria = angular.copy($scope.listaArquivosAjuda[index - 1]);
                   $scope.listaArquivosAjuda[index - 1] = angular.copy($scope.listaArquivosAjuda[index]);
                   $scope.listaArquivosAjuda[index] = posicaoTemporaria;
                 }
               }
               $scope.descerPosicao = function (index) {
                 var tamanho = $scope.listaArquivosAjuda.length;
                 if (index < (tamanho - 1)) {
                   var posicaoTemporaria = angular.copy($scope.listaArquivosAjuda[index + 1]);
                   $scope.listaArquivosAjuda[index + 1] = angular.copy($scope.listaArquivosAjuda[index]);
                   $scope.listaArquivosAjuda[index] = posicaoTemporaria;
                 }
               }

               $scope.limpar = function(){
                 $scope.arquivoAjuda = {
                   arquivos: [],
                   status: true
                 };
                 $scope.indexEditar  = undefined;
                 $scope.editar = false;
               };

               $scope.removerArquivo = function (event, arquivo) {
                 var index = $scope.arquivoAjuda.arquivos.indexOf(arquivo);
                 $scope.arquivoAjuda.arquivos.splice(index, 1);
               };

               $scope.removerDados = function (arquivoAjuda) {
                 var index = $scope.listaArquivosAjuda.indexOf(arquivoAjuda);
                 for(var i=0; i < servico.campoArquivoAjuda.length && $scope.listaArquivosAjuda[index].id ;i++){
                   if (servico.campoArquivoAjuda[i].id ==  arquivoAjuda.id) {
                     servico.campoArquivoAjuda[i].status = false;
                   }
                 }
                 $scope.listaArquivosAjuda.splice(index, 1);
               };

               $scope.closeDialog = function () {
                 $mdDialog.cancel();
               };

               $scope.editarDados = function(arquivoAjuda){
                 $scope.editar = true;
                 angular.forEach($scope.listaArquivosAjuda, function(item, $index){
                   if(item.nomeCampo == arquivoAjuda.nomeCampo){
                     $scope.arquivoAjuda = angular.copy(item);
                     $scope.indexEditar = $index;
                   }
                 });
               };

               $scope.adicionar = function(){

                 if($scope.formArquivosAjuda.$valid){

                   if($scope.editar){
                     $scope.listaArquivosAjuda[$scope.indexEditar]
                       = angular.copy($scope.arquivoAjuda);
                   }else{
                     $scope.listaArquivosAjuda
                       .push(angular.copy($scope.arquivoAjuda));
                   }

                   $scope.limpar();
                   comumServiceServico.resertarFormulario($scope, 'formArquivosAjuda');

                 }else{
                   comumServiceServico
                     .mostrarMensagemError('Necessário preencher os campos obrigatórios');
                 }
               };

               $scope.confirmar = function(){
                 callback($scope.listaArquivosAjuda);
                 $scope.closeDialog();
               };

               init();
             }],
             templateUrl: 'scripts-servicos/pages/servicos/view/partials/arquivosAjuda.html',
             parent: angular.element(document.body),
             targetEvent: ev,
             clickOutsideToClose: false,
             locals: {
             }
           }).then(function () {

           });
         };

        var init = function(){
          // buscarArquivosAjuda();
        };

        init();

        return {
          isArquivosAjuda: isArquivosAjuda,
          abrirModalArquivosAjuda: abrirModalArquivosAjuda
        };

      }]);
})();

(function(){

    'use strict';

    angular
        .module('su-servicos')
        .controller('servicosListController',  ['$scope','servicoService','$state', '$mdDialog',
                    '$mdToast','triagemRequerimentoService',
        function ($scope, servicoService, $state, $mdDialog,
                  $mdToast, triagemRequerimentoService) {

        var init = function() {
            $scope.servicos = [];
            $scope.minDate = new Date();
            $scope.registroInicial = 1;
            $scope.tamanhoLimite = 5;
            $scope.totalItems = 0;
            $scope.updateTable();
            $scope.buscarSituacoes();
        };

        $scope.buscarSituacoes = function () {
            triagemRequerimentoService.findAllStatus().then(function (resp) {
              $scope.situacoes = resp.data.resposta[0];
            });
        };

        $scope.updateTable = function(){
            $scope.carregando = true;
            servicoService.findAll(($scope.registroInicial - 1), $scope.tamanhoLimite).then(function (resp) {
                $scope.servicos = resp.data.resposta[0].content;
                $scope.totalItems = resp.data.resposta[0].totalElements;
            });
        };

        $scope.filterFindAll = function() {
            servicoService.filterFindAll($scope.titulo, $scope.situacao, ($scope.registroInicial - 1), $scope.tamanhoLimite).then(function(resp) {
                $scope.servicos = resp.data.resposta[0].content;
                $scope.totalItems = resp.data.resposta[0].totalElements;
            });
        };

        $scope.findPagination = function() {
            if ($scope.titulo != undefined && $scope.titulo != "") {
                $scope.filterFindAll();
            } else {
                $scope.updateTable();
            }
        };

        $scope.adicionarServico = function(e){
          e.preventDefault();
            $state.go('servicos.SERVICOS_CADASTRAR_SERVICOS');
        };

        $scope.clearFilter = function() {
            $scope.titulo = "";
            $scope.situacao = null;
        };

        $scope.showConfirmExcluir = function(ev, id) {
            var confirm = $mdDialog.confirm({focusOnOpen:false})
                .title('Excluir Serviço')
                .textContent('Deseja excluir dados?')
                .targetEvent(ev)
                .ok('Sim')
                .cancel('Não');
            $mdDialog.show(confirm).then(function() {
                servicoService.deletar(id).then(function(resposta){
                    $scope.updateTable();
                });
            }, function() {
                $mdDialog.cancel();
            });
        };

        $scope.novoCadastro = function() {
            $state.go('servicos.SERVICOS_CADASTRAR_SERVICOS');
        };

        $scope.toggleAtivo = function(ev, servico) {
            if (servico.ativo == true) {
                var confirm = $mdDialog.confirm()
                    .title('Ativar Serviço')
                    .textContent('Deseja confirmar a reativação do serviço?')
                    .targetEvent(ev)
                    .ok('Sim')
                    .cancel('Não');

                $mdDialog.show(confirm).then(function() {
                    servicoService.toggleAtivo(servico).then(function (resposta) {
                        $scope.updateTable();
                    });
                }, function() {
                    servico.ativo = false;
                    $mdDialog.cancel();
                });
            } else if (servico.ativo == false) {
                abrirModalSuspender(ev,servico);
            }
        };

        var abrirModalSuspender = function(ev, servico) {
            $mdDialog.show({
                controller: ['$scope', function($scope){

                $scope.servico = servico;

                  $scope.confirmar = function () {
                      if ($scope.servico.justificativa != undefined) {
                          servicoService.toggleAtivo($scope.servico).then(function (resposta) {
                              $mdDialog.cancel();
                          });
                      } else {
                          $mdToast.show($mdToast.simple()
                              .content("Campo justificativa é obrigatório!")
                              .theme("error-toast")
                              .position('top right'));
                      }
                  };

                  $scope.closeDialog = function() {
                      servico.ativo = true;
                      $mdDialog.cancel();
                  };

                }],
                templateUrl: 'scripts-servicos/pages/servicos/view/partials/suspender.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose:true
            }).then(function(resposta){
                $scope.updateTable();
            });
        };

        init();

    }]);

})();

(function(){

    'use strict';

    angular
        .module('su-servicos')
        .controller('cadastrarServicosController',
                      ['$scope', '$stateParams', '$state', '$mdDialog', 'servicoService', 'DominioService', '$rootScope',
                      'comumServiceServico', 'solicitanteService', '$timeout', '$q', '$filter', 'FormularioFactory', 'arquivoServiceServicos', 'cloneUtils',
                      'modalSolicitanteFactory', 'modalDocumentosFactory', 'modalSubsecoesFactory', 'modalDeclaracaoFactory', 'modalDadosComplementaresFactory', 'modalArquivosAjudaFactory', 'tipoProcessoSeiService', '$element',
            function ($scope, $stateParams, $state, $mdDialog, servicoService, DominioService, $rootScope,
                      comumServiceServico, solicitanteService, $timeout, $q, $filter, FormularioFactory, arquivoServiceServicos, cloneUtils,
                      modalSolicitanteFactory, modalDocumentosFactory, modalSubsecoesFactory, modalDeclaracaoFactory, modalDadosComplementaresFactory, modalArquivosAjudaFactory, tipoProcessoSeiService, $element) {

            $element.find('input').on('keydown', function(ev) {
                ev.stopPropagation();
            });

            var vm = $scope;
            vm.minDate = new Date();
            vm.isNew = !($stateParams.id);

            vm.tipoSei;

            vm.tipoSegmento = DominioService.tipoSegmento();
            vm.ordenaLista = servicoService.ordenaLista;
            vm.isNumeroInteiro = comumServiceServico.isNumeroInteiro;

            vm.REQUERIMENTO_CONSULTA_DOMINIALIDADE = 'consultaDominialidade';
            vm.REQUERIMENTO_GESTAO_PRAIAS = 'requerimentoAdesaoGestaoPraia';
            vm.SERVICO_GENERICO_SEM_RIP = 'servicoGenericoSemRip';
            vm.ID_SERVICO_GENERICO_SEM_RIP_VENAL = 20;
            vm.SERVICO_GENERICO_COM_RIP = 'servicoGenericoComRip';
            vm.PARCELAMENTO_DEBITOS = 'parcelamentoDebitos';

            vm.isDadosComplementar = modalDadosComplementaresFactory.isDadosComplementar;
            vm.isSubsecao = modalSubsecoesFactory.isSubsecao;
            vm.isArquivosAjuda = modalArquivosAjudaFactory.isArquivosAjuda;
            var isCampoUFAtendimento = modalDadosComplementaresFactory.isCampoUFAtendimento;
            var adicionarServicoGenericoUFAtendimento = modalDadosComplementaresFactory
                                                        .adicionarServicoGenericoUFAtendimento;

            var buscarArquivosAjuda = function(id){
              servicoService.buscarArquivoAjuda(id)
                .then(function (resposta) {
                  vm.listas.arquivosAjuda = resposta.data.resposta;
                });

            };

              var apagarArquivoAjuda = function (id) {
                servicoService.apagarArquivoAjuda(id);
              };


            var init = function () {

                vm.servico = {
                    solicitantes: [],
                    documentos: [],
                    analisesTecnicas: [],
                    declaracoes: [],
                    triagens: [],
                    listUnidadeCentral: [],
                    conclusaoServicoTriagem: [],
                    brasilCidadao: {}
                };

                vm.listas = {
                  solicitante: [],
                  documento: [],
                  analiseTecnica: [],
                  declaracao: [],
                  triagem: [],
                  dadosComplementar: [],
                  arquivosAjuda: [],
                  subsecoesDadoComplementar: []
                };

                vm.concluirServicoTriagem = false;

                vm.palavrasChaves = [];
                vm.formularios = [];
                vm.listaFormatosArquivos = [];
                vm.listJurisdicoes = $rootScope.usuarioLogado.jurisdicoes;

                vm.tiposDocumento = [{chave: 'OBRIGATORIO', valor: 'Obrigatório'}, {
                      chave: 'COMPLEMENTAR',
                      valor: 'Complementar'
                }];

                $scope.searchTerm;
                $scope.clearSearchTerm = function() {
                  $scope.searchTerm = '';
                };

                vm.queryDocumento = servicoService.getQueryDefault();
                vm.querySolicitante = servicoService.getQueryDefault();
                vm.queryDeclaracoes = servicoService.getQueryDefault();
                vm.queryDados = servicoService.getQueryDefault();
                vm.queryDadosSubsecao = servicoService.getQueryDefault();

                vm.selected = [];
                vm.listaTiposDocumento = [];
                carregarTipoDocumento();

                tipoProcessoSeiService.listarTiposProcessoSei().then(function (resposta) {
                  $scope.listTipos = resposta.data.resposta;
                  vm.tipoSei = vm.listTipos.find(vm.findTipoProcessoPorId);
                });

                if (vm.isNew) {
                    vm.resetServico();
                } else {

                    vm.tituloStatico = $filter('translate')('label-titulo');

                    servicoService.findById($stateParams.id).then(function (resposta) {

                        getDadosServico(resposta);
                        buscarArquivosAjuda($scope.servico.id);
                        tratarArquivo();
                        habilitaConclusaoTriagem();

                        angular.forEach(vm.servico.solicitantes, function (solicitante) {
                            if (solicitante.status) {
                                vm.listas.solicitante.push(solicitante);
                            }
                        });

                        angular.forEach(vm.servico.documentos, function (documento) {
                            if (documento.status) {
                                vm.listas.documento.push(documento);
                            }
                        });

                        angular.forEach(vm.servico.analisesTecnicas, function (analiseTecnica) {
                            if (analiseTecnica.status) {
                                vm.listas.analiseTecnica.push(analiseTecnica);
                            }
                        });

                        angular.forEach(vm.servico.declaracoes, function (declaracao) {
                            if (declaracao.status) {
                                vm.listas.declaracao.push(declaracao);
                            }
                        });

                        angular.forEach(vm.servico.triagens, function (triagem) {
                          if (triagem.status) {
                            vm.listas.triagem.push(triagem);
                          }
                        });

                        angular.forEach(vm.servico.listaUnidadeCentral, function (unidadeCentral) {
                          if(unidadeCentral.uf){
                            if(angular.isUndefined(vm.servico.listUnidadeCentral)){
                              vm.servico.listUnidadeCentral = [];
                            }
                            vm.servico.listUnidadeCentral.push(unidadeCentral.uf);
                          }
                        })

                        angular.forEach(vm.servico.campoDadoComplementar, function (dados) {
                          if (dados.status && !isCampoUFAtendimento(dados)) {
                            vm.listas.dadosComplementar.push(dados);
                          }
                        });

                        angular.forEach(vm.servico.campoArquivoAjuda, function (arquivoAjuda) {
                          if (arquivoAjuda.status) {
                            vm.listas.arquivosAjuda.push(arquivoAjuda);
                          }
                        });

                        angular.forEach(vm.servico.subsecoesDadoComplementar, function (subsecao) {
                          if (subsecao.status) {
                            vm.listas.subsecoesDadoComplementar.push(subsecao);
                          }
                        });

                        for(var i = 0; i < vm.listas.subsecoesDadoComplementar.length; i++){
                          for(var j = 0; j < vm.listas.subsecoesDadoComplementar[i].campos.length; j++){
                            for(var z = 0; z < vm.listas.dadosComplementar.length; z++){
                              if(vm.listas.subsecoesDadoComplementar[i].campos[j].id === vm.listas.dadosComplementar[z].id){
                                vm.listas.subsecoesDadoComplementar[i].campos[j] = vm.listas.dadosComplementar[z];
                                vm.listas.dadosComplementar[z].hasSubsecao = true;
                              }
                            }
                          }
                        }

                      carregarPermissaoAcessos();

                    });
                }
                carregarTipoAssuntos();
                carregarFormularioWeb();
                carregarListaNiveisAutenticacao();
                carregarFormatosArquivo();
                carregarGruposUnidadeSei();

            };

            vm.checkBrasil = function(){
              if(!vm.servico.brasilCidadao.exigirAutenticacaoBrasilCidadao){
                vm.servico.brasilCidadao.nivelAutenticacao = undefined;
              }
            };

            vm.$watch('servico.link', function(value){
                if(value && value.length > 0){
                     vm.servico.analisesTecnicas = [];
                     vm.listas.analiseTecnica = [];

                     vm.servico.declaracoes = [];
                     vm.listas.declaracao = [];
                     vm.servico.listUnidadeCentral = [];

                     vm.servico.etapaTriagem = false;
                     vm.servico.retirarDadosImovel = false;
                     vm.servico.habilitarPriorizacaAtendimento = false;
                }
            });

            vm.selecionarFormulario = function(){
                if(vm.servico.formularioWeb){
                    vm.servico.etapaTriagem = true;
                    if(vm.SERVICO_GENERICO_SEM_RIP){
                      vm.servico.retirarDadosImovel = false;
                    }
                    if(vm.servico.formularioWeb.rota==vm.PARCELAMENTO_DEBITOS){
                      vm.servico.brasilCidadao.exigirAutenticacaoBrasilCidadao = true;
                    }
                }
            };

            vm.selecionarEtapaTriagem = function(){
                vm.servico.unidadeSEI = undefined;
            };

            var carregarGruposUnidadeSei = function () {
                servicoService.listarGruposUnidadeSei().then(function (resp) {
                    vm.gruposSei = resp.data.resposta;
                });
            };

            var carregarTipoDocumento = function () {
                servicoService.listarTipoDocumento().then(function (resp) {
                    vm.listaTiposDocumento = resp.data.resposta;
                });
            };

            var getDadosServico = function (resposta) {
                vm.servico = resposta.data.resposta[0];
                vm.tipoSei = vm.listTipos.find(vm.findTipoProcessoPorId);

                if(angular.isDefined(vm.servico.grupoUnidadeSEI)){
                    vm.servico.unidadeSEI = vm.servico.grupoUnidadeSEI[0];
                }

                servicoService.formatarPalavrasChavesSalvas(vm.servico, function (palavrasChaves) {
                  vm.servico.palavrasChaves = [];
                  vm.palavrasChaves = palavrasChaves;
                });
            };

            var carregarTipoAssuntos = function () {
                comumServiceServico.buscarTiposAssuntos().then(function (resposta) {
                    vm.assuntos = resposta.data.resposta;
                });
            };

            var carregarPermissaoAcessos = function () {
              if(vm.servico.permissaoAcessos == null || angular.isUndefined(vm.servico.permissaoAcessos)){
                vm.servico.permissaoAcessos = (vm.servico.formularioWeb === '') ?
                  undefined : vm.servico.formularioWeb.permissao;
              }
            };

            var carregarFormularioWeb = function () {
                comumServiceServico.listarFormularioWeb().then(function (resposta) {
                    vm.formularios = resposta.data.resposta;
                });
            };

              var carregarListaNiveisAutenticacao = function () {
                servicoService.findAllNiveisAutenticacao().then(function (resposta) {
                  vm.listaNiveisAutenticacao = resposta.data.resposta[0];
                });
              };

            var carregarFormatosArquivo = function () {
                comumServiceServico.listarFormatosArquivo().then(function (resposta) {
                    vm.listaFormatosArquivos = resposta.data.resposta;
                });
            };

            var tratarArquivo = function(){
                if(vm.servico.arquivo !== null){
                    vm.servico.arquivo.name = vm.servico.arquivo.nome;
                }
            };

            vm.resetServico = function () {
                vm.palavrasChaves = [];
                vm.servico = {
                  solicitantes: [],
                  documentos: [],
                  ativo: true,
                  palavrasChaves: [],
                  prazoAtendimentoTriagem: {
                    tipoPrazoAtendimento: 'SEM_PRAZO_DEFINIDO'
                  },
                  prazoAtendimentoAnaliseTecnica: {
                    tipoPrazoAtendimento: 'SEM_PRAZO_DEFINIDO'
                  },
                  idTipoProcessoSei: 1,
                  brasilCidadao: {}                  
                };
            

                if (angular.isDefined(vm.manterServicos)) {
                    vm.manterServicos.$setUntouched();
                }

                if (vm.listTipos){
                  vm.tipoSei = vm.listTipos.find(vm.findTipoProcessoPorId);
                }

            };

            vm.removerSolicitante = function (ev, item) {
                modalSolicitanteFactory
                        .abrirModalRemoverSolicitante(ev, item, vm.listas, vm.servico);
            };

            vm.removerDocumentos = function (ev, item) {
                var confirm = $mdDialog.confirm({focusOnOpen: false})
                    .title($filter('translate')('label-documentos'))
                    .textContent($filter('translate')('mensagem-confirmar-exclusao'))
                    .targetEvent(ev)
                    .ok($filter('translate')('sim'))
                    .cancel($filter('translate')('nao'));

                $mdDialog.show(confirm).then(function () {

                    var index = vm.listas.documento.indexOf(item);

                    if (vm.listas.documento[index].id) {
                        var indexServico = vm.servico.documentos.indexOf(item);
                        vm.servico.documentos[indexServico].status = false;
                    }

                    vm.listas.documento.splice(index, 1);

                }, function () {
                    $mdDialog.cancel();
                });
            };

            vm.removerDeclaracoes = function (ev, item) {
                var confirm = $mdDialog.confirm({focusOnOpen: false})
                    .title($filter('translate')('label-declaracao'))
                    .textContent($filter('translate')('mensagem-confirmar-exclusao'))
                    .targetEvent(ev)
                    .ok($filter('translate')('sim'))
                    .cancel($filter('translate')('nao'));

                $mdDialog.show(confirm).then(function () {

                    var index = vm.listas.declaracao.indexOf(item);

                    if (vm.listas.declaracao[index].id) {
                        var indexDeclaracao = vm.servico.declaracoes.indexOf(item);
                        vm.servico.declaracoes[indexDeclaracao].status = false;
                    }

                    vm.listas.declaracao.splice(index, 1);

                }, function () {
                    $mdDialog.cancel();
                });
            };

            vm.removerDadosComplementares = function (ev, item) {
                var confirm = $mdDialog.confirm({focusOnOpen: false})
                    .title($filter('translate')('label-documentos'))
                    .textContent($filter('translate')('mensagem-confirmar-exclusao'))
                    .targetEvent(ev)
                    .ok($filter('translate')('sim'))
                    .cancel($filter('translate')('nao'));

                $mdDialog.show(confirm).then(function () {

                    var index = vm.listas.dadosComplementar.indexOf(item);

                    if (vm.listas.dadosComplementar[index].id) {
                        var indexServico = vm.servico.campoDadoComplementar.indexOf(item);
                        vm.servico.campoDadoComplementar[indexServico].status = false;
                    }
                    vm.listas.dadosComplementar[index].removed = true;

                    vm.listas.dadosComplementar.splice(index, 1);

                }, function () {
                    $mdDialog.cancel();
                });
            };

            vm.removerArquivosAjuda = function (ev, item) {
                var confirm = $mdDialog.confirm({focusOnOpen: false})
                    .title($filter('translate')('label-documentos'))
                    .textContent($filter('translate')('mensagem-confirmar-exclusao'))
                    .targetEvent(ev)
                    .ok($filter('translate')('sim'))
                    .cancel($filter('translate')('nao'));

                $mdDialog.show(confirm).then(function () {

                    var index = vm.listas.arquivosAjuda.indexOf(item);

                    if (vm.listas.arquivosAjuda[index].id) {
                      var indexServico = indexOf(vm.servico.campoArquivoAjuda, item);
                      vm.servico.campoArquivoAjuda[indexServico].status = false;
                    }

                    vm.listas.arquivosAjuda.splice(index, 1);

                }, function () {
                    $mdDialog.cancel();
                });
            };

            vm.verificaDigitos = function (idPortal){
              if (idPortal != undefined){
                return 5;
              } else {
                return 0;
              }
            }

            vm.mostrarResultado = function (box, num_max, campospan) {
              document.getElementById(campospan).innerHTML = box.length + " / 5";
            };

            function indexOf(lista, elemento) {
              for (var i = 0; i < lista.length; i++) {
                if (lista[i].id === elemento.id) {
                   return i;
                }
              }
            }

            vm.subirPosicao = function (index) {
              if (index > 0) {
                var posicaoTemporaria = angular.copy(vm.listas.arquivosAjuda[index - 1]);
                vm.listas.arquivosAjuda[index - 1] = angular.copy(vm.listas.arquivosAjuda[index]);
                vm.listas.arquivosAjuda[index] = posicaoTemporaria;
              }
            }
            vm.descerPosicao = function (index) {
              var tamanho = vm.listas.arquivosAjuda.length;
              if (index < (tamanho - 1)) {
                var posicaoTemporaria = angular.copy(vm.listas.arquivosAjuda[index + 1]);
                vm.listas.arquivosAjuda[index + 1] = angular.copy(vm.listas.arquivosAjuda[index]);
                vm.listas.arquivosAjuda[index] = posicaoTemporaria;
              }
            }

            vm.subirPosicaoDadoComplementar = function (index) {
              if (index > 0) {
                var posicaoTemporaria = vm.listas.dadosComplementar[index - 1];
                vm.listas.dadosComplementar[index - 1] = vm.listas.dadosComplementar[index];
                vm.listas.dadosComplementar[index] = posicaoTemporaria;
              }
            };
            vm.descerPosicaoDadoComplementar = function (index) {
              var tamanho = vm.listas.dadosComplementar.length;
              if (index < (tamanho - 1)) {
                var posicaoTemporaria = vm.listas.dadosComplementar[index + 1];
                vm.listas.dadosComplementar[index + 1] = vm.listas.dadosComplementar[index];
                vm.listas.dadosComplementar[index] = posicaoTemporaria;
              }
            };

            vm.subirPosicaoSubsecao = function (index) {
              if (index > 0) {
                var posicaoTemporaria = vm.listas.subsecoesDadoComplementar[index - 1];
                vm.listas.subsecoesDadoComplementar[index - 1] = vm.listas.subsecoesDadoComplementar[index];
                vm.listas.subsecoesDadoComplementar[index] = posicaoTemporaria;
              }
            };
            vm.descerPosicaoSubsecao = function (index) {
              var tamanho = vm.listas.subsecoesDadoComplementar.length;
              if (index < (tamanho - 1)) {
                var posicaoTemporaria = vm.listas.subsecoesDadoComplementar[index + 1];
                vm.listas.subsecoesDadoComplementar[index + 1] = vm.listas.subsecoesDadoComplementar[index];
                vm.listas.subsecoesDadoComplementar[index] = posicaoTemporaria;
              }
            };

            vm.removerSubsecoes = function (ev, item) {
                var confirm = $mdDialog.confirm({focusOnOpen: false})
                    .title($filter('translate')('label-documentos'))
                    .textContent($filter('translate')('mensagem-confirmar-exclusao'))
                    .targetEvent(ev)
                    .ok($filter('translate')('sim'))
                    .cancel($filter('translate')('nao'));

                $mdDialog.show(confirm).then(function () {

                    var index = vm.listas.subsecoesDadoComplementar.indexOf(item);

                    if (vm.listas.subsecoesDadoComplementar[index].id) {
                      var indexServico = indexOf(vm.servico.subsecoesDadoComplementar, item);
                      vm.servico.subsecoesDadoComplementar[indexServico].status = false;
                    }

                    vm.listas.subsecoesDadoComplementar.splice(index, 1);

                }, function () {
                    $mdDialog.cancel();
                });
            };


            vm.removerDocumento = function (ev, index) {
                vm.listas.documento.splice(index, 1);
            };

            vm.editSolicitante = function (ev, item) {

                var solicitantes = (vm.listas.solicitante || []);

                $mdDialog.show({
                    controller:  ['$scope', 'tipoSegmento', function ($scope, tipoSegmento) {

                      var init = function(){

                          $scope.tipoSegmento = tipoSegmento;
                          $scope.solicitantes = (solicitantes)
                                      ? angular.copy(solicitantes) : [];

                          $scope.edit = angular.isDefined(item);
                          $scope.itemOld = angular.copy(item);
                          $scope.novoSolicitante = {};

                          if(item){

                              angular.forEach($scope.solicitantes, function(s){
                                  if(s.id && s.id == item.id){
                                      $scope.novoSolicitante = angular.copy(s);
                                  }else if (s.tipo == item.tipo   && s.requisitos == item.requisitos) {
                                      $scope.novoSolicitante = angular.copy(s);
                                  }
                              });
                          }

                          $scope.querySolicitante = servicoService.getQueryDefault();
                      }

                      $scope.addSolicitante = function ($event) {

                        modalSolicitanteFactory.adicionar($event,
                              $scope.adicionarSolicitantes, vm.listas,
                              $scope.solicitantes, $scope.novoSolicitante,
                              $scope.itemOld, $scope.edit,
                          function () {
                            $scope.edit = false;
                            $scope.novoSolicitante = undefined;
                          });

                      };

                      $scope.editar = function ($event, item) {
                          var index = $scope.solicitantes.indexOf(item);
                          $scope.novoSolicitante = angular.copy($scope.solicitantes[index]);
                          $scope.itemOld = angular.copy($scope.novoSolicitante);
                          $scope.edit = true;
                      };

                      $scope.remover = function ($event, item) {
                        modalSolicitanteFactory.remover($event, vm.servico, vm.listas,
                            $scope.solicitantes, item);
                      };

                      $scope.closeDialog = function () {
                          $mdDialog.cancel();
                      };

                      $scope.confirmar = function () {
                          $mdDialog.hide($scope.solicitantes);
                      };

                      init();

                    }],
                    templateUrl: 'scripts-servicos/pages/servicos/view/partials/solicitante.html',
                    parent: angular.element(document.body),
                    targetEvent: ev,
                    clickOutsideToClose: false,
                    locals: {
                        tipoSegmento: $scope.tipoSegmento,
                        solicitantes: angular.copy(vm.listas.solicitante)
                    }
                }).then(function (solicitantes) {
                    vm.listas.solicitante = solicitantes;
                });
            };

            vm.editDocumento = function (ev, item) {

                var solicitantes = vm.listas.solicitante;

                $mdDialog.show({
                    controller: ['$scope', 'documentos', function ($scope, documentos) {

                        var iniciarFormulario = function () {

                            modalDocumentosFactory.modificarIndiceSolicitante($scope.novoDocumento);
                            modalDocumentosFactory.selecionarCheckboxsFormatosArq($scope.novoDocumento,
                              vm.listaFormatosArquivos);
                        };

                        var init = function(){

                            $scope.documentos = (documentos || []);
                            $scope.solicitantes = (solicitantes || []);

                            $scope.edit = angular.isDefined(item);
                            $scope.itemOld = angular.copy(item);
                            $scope.novoDocumento = {};
                            $scope.formatoArquivos = [];

                            if(item){
                                angular.forEach($scope.documentos, function(s){
                                    if(s.id && s.id == item.id){
                                        $scope.novoDocumento = angular.copy(s);
                                    } else if (s.tipo == item.tipo && s.nome == item.nome) {
                                        $scope.novoDocumento = angular.copy(s);
                                    }
                                });
                            }

                            $scope.queryDocumento = servicoService.getQueryNome();
                            $scope.ordemDocumento = vm.ordenaLista;
                            $scope.listaFormatosArquivos = vm.listaFormatosArquivos;
                            $scope.tiposDocumento = vm.tiposDocumento;
                            $scope.listaTiposDocumento = vm.listaTiposDocumento;

                            iniciarFormulario();
                            $scope.solicitantes = modalDocumentosFactory
                                              .setarSolicitantes($scope.solicitantes);
                        };

                        var limparFormatos = function(){
                          modalDocumentosFactory.limparFormatosSelecionados($scope.listaFormatosArquivos,
                            function () {
                              $scope.statusExtensaoDocumento = false;
                              $scope.outraExtensaoDocumento = "";
                            });
                        };

                        $scope.addDocumento = function ($event) {

                          modalDocumentosFactory.addDocumento(
                            $event, $scope.adicionarDocumentos, $scope.documentos,
                            $scope.novoDocumento, $scope.statusExtensaoDocumento,
                            $scope.outraExtensaoDocumento, $scope.listaFormatosArquivos,
                            $scope.itemOld, $scope.edit, function(){

                              $scope.edit = false;

                              $scope.novoDocumento = {
                                nome: undefined
                              };

                              limparFormatos();
                              $scope.adicionarDocumentos.$submitted = false;
                            });
                        };

                        $scope.editar = function ($event, item) {
                            var index = $scope.documentos.indexOf(item);
                            $scope.novoDocumento = angular.copy($scope.documentos[index]);
                            $scope.itemOld = angular.copy($scope.novoDocumento);
                            $scope.edit = true;

                            iniciarFormulario();
                        };

                        $scope.remover = function ($event, item) {
                          modalDocumentosFactory.remover($event, $scope.documentos,
                            vm.servico, item);
                        };

                        $scope.closeDialog = function () {
                            $mdDialog.cancel();
                        };
                        $scope.confirmar = function () {
                            servicoService.setarOrdem($scope.documentos);
                            $mdDialog.hide($scope.documentos);
                        };

                        init();

                    }],
                    templateUrl: 'scripts-servicos/pages/servicos/view/partials/documento.html',
                    parent: angular.element(document.body),
                    targetEvent: ev,
                    clickOutsideToClose: false,
                    locals: {
                        documentos: angular.copy(vm.listas.documento),
                        solicitantes: angular.copy(vm.listas.solicitante)
                    }
                }).then(function (documentos) {
                    vm.listas.documento = documentos;
                });
            };


            vm.modalDeclaracoes = function (ev) {
                var declaracoes = vm.listas.declaracao
                          ? angular.copy(vm.listas.declaracao) : [];

                var solicitantes = vm.listas.solicitante;

                $mdDialog.show({
                    controller: ['$scope', function ($scope) {

                        var init = function(){
                            $scope.declaracoes = (declaracoes || []);
                            $scope.solicitantes = (solicitantes || []);
                            $scope.novoItemDeclaracao = {
                                item: undefined
                            };

                            $scope.queryDeclaracao = servicoService.getQueryDefault();
                        };

                        $scope.validar = function(){
                            return modalDeclaracaoFactory
                                  .validar($scope.formDeclaracao,
                                    $scope.novoItemDeclaracao, $scope.declaracoes);
                        };

                        $scope.addItemDeclaracao = function ($event) {

                             if(!$scope.formDeclaracao.invalid){

                                $scope.novoItemDeclaracao.status = true;
                                $scope.declaracoes.push($scope.novoItemDeclaracao);

                                $scope.novoItemDeclaracao = {};
                             }
                        };

                        $scope.removerDeclaracao = function ($event, index) {
                            $scope.declaracoes.splice(index, 1);
                        };
                        $scope.closeDialog = function () {
                            $mdDialog.cancel();
                        };
                        $scope.confirmarDeclaracoes = function () {
                            servicoService.setarOrdem($scope.declaracoes);
                            vm.listas.declaracao = $scope.declaracoes;
                            $mdDialog.hide();
                        };

                        $scope.ordemDeclaracao = vm.ordenaLista;

                        init();

                    }],
                    templateUrl: 'scripts-servicos/pages/servicos/view/partials/declaracoes.html',
                    parent: angular.element(document.body),
                    targetEvent: ev,
                    clickOutsideToClose: false,
                    locals: {
                        declaracoes: angular.copy($scope.declaracoes)
                    }
                });
            };

            var validarLinkOuFormularioWeb = function () {
                vm.servico.link = vm.servico.link == '' ? undefined : vm.servico.link;
                if ((vm.servico.link == undefined)
                    && (vm.servico.formularioWeb == undefined || vm.servico.formularioWeb == '')) {
                    var msg = 'É necessário informar um link ou formulário web.';
                    comumServiceServico.mostrarMensagemError(msg);
                    throw msg;
                }
            };

            var validarSolicitantes = function () {
                if (vm.isFormulario()) {

                    var error = function(){
                        var msg = 'É necessário informar pelo menos um solicitante.';
                        comumServiceServico.mostrarMensagemError(msg);
                        throw msg;
                    };

                    if (!vm.servico.solicitantes ||
                                vm.servico.solicitantes.length == 0) {
                        error();
                    }

                    var exists = false;

                    angular.forEach(vm.servico.solicitantes, function(item){
                        if(item.status){
                                exists = true;
                        }
                    });

                    if(!exists){
                        error();
                    }

                }
            };

            var validarDeclaracoes = function () {
                if (vm.isFormulario()) {

                    var error = function(){
                        var msg = 'É necessário informar pelo menos uma declaração.';
                        comumServiceServico.mostrarMensagemError(msg);
                        throw msg;
                    };

                    if (!vm.servico.declaracoes ||
                            vm.servico.declaracoes.length == 0) {
                        error();
                    }

                    var exists = false;

                    angular.forEach(vm.servico.declaracoes, function(item){
                        if(item.status){
                                exists = true;
                        }
                    });

                    if(!exists){
                        error();
                    }

                }
            };

            vm.isFormulario = function () {
                return vm.servico.formularioWeb;
            };

            var reiniciarListas = function () {
              if (!vm.servico.id) {
                vm.servico.solicitantes = [];
                vm.servico.documentos = [];
                vm.servico.analisesTecnicas = [];
                vm.servico.declaracoes = [];
                vm.servico.triagens = [];
              }
              if (!vm.servico.unidadeCentral){
                vm.servico.listUnidadeCentral=[];
              }
            };

            var atualizarServico = function(){
                reiniciarListas();
                angular.forEach(vm.listas.solicitante, function (item) {

                    if(item.id == undefined || item.id == 0) {
                        if(!vm.servico.solicitantes){
                             vm.servico.solicitantes = [];
                         }
                         vm.servico.solicitantes.push(item);
                    }else{

                        for(var i=0; i<vm.servico.solicitantes.length; i++){
                            if(item.id == vm.servico.solicitantes[i].id){
                                vm.servico.solicitantes[i] = angular.copy(item);
                                return;
                            }
                        }
                    }
                });

                angular.forEach(vm.listas.documento, function (item) {
                    if (item.id == undefined || item.id == 0) {
                         if(!vm.servico.documentos){
                             vm.servico.documentos = [];
                         }
                         vm.servico.documentos.push(item);
                    }else{

                        for(var i=0; i<vm.servico.documentos.length; i++){
                            if(item.id == vm.servico.documentos[i].id){
                                vm.servico.documentos[i] = angular.copy(item);
                                return;
                            }
                        }
                    }
                });

                angular.forEach(vm.listas.analiseTecnica, function (item) {
                    if (item.id == undefined || item.id == 0) {
                         if(!vm.servico.analisesTecnicas){
                             vm.servico.analisesTecnicas = [];
                         }
                         vm.servico.analisesTecnicas.push(item);
                    }else{
                        for(var i=0; i<vm.servico.analisesTecnicas.length; i++){
                            if(item.id == vm.servico.analisesTecnicas[i].id){
                                vm.servico.analisesTecnicas[i] = angular.copy(item);
                                return;
                            }
                        }
                    }
                });

                angular.forEach(vm.listas.declaracao, function (item) {
                     if (item.id === undefined || item.id === 0) {
                         if(!vm.servico.declaracoes){
                             vm.servico.declaracoes = [];
                         }
                         vm.servico.declaracoes.push(item);
                     }else{
                         for(var i=0; i<vm.servico.declaracoes.length; i++){
                            if(item.id == vm.servico.declaracoes[i].id){
                                vm.servico.declaracoes[i] = angular.copy(item);
                                return;
                            }
                        }
                     }
                });

                angular.forEach(vm.listas.triagem, function (item) {
                  if (item.id == undefined || item.id == 0) {
                    if(!vm.servico.triagens){
                      vm.servico.triagens = [];
                    }
                    vm.servico.triagens.push(item);
                  }else{
                    for(var i=0; i<vm.servico.triagens.length; i++){
                      if(item.id == vm.servico.triagens[i].id){
                        vm.servico.triagens[i] = angular.copy(item);
                        return;
                      }
                    }
                  }
                });

                 angular.forEach(vm.listas.dadosComplementar, function (item, $index) {
                   vm.listas.dadosComplementar[$index].posicao = $index;
                    if (angular.isUndefined(item.id)) {
                         if(!vm.servico.campoDadoComplementar){
                             vm.servico.campoDadoComplementar = [];
                         }
                         vm.servico.campoDadoComplementar.push(item);

                    }else{
                        for(var i=0; i<vm.servico.campoDadoComplementar.length; i++){
                            if(item.id == vm.servico.campoDadoComplementar[i].id){
                                vm.servico.campoDadoComplementar[i] = angular.copy(item);
                                return;
                            }
                        }
                    }
                });

                 angular.forEach(vm.listas.arquivosAjuda, function (item, $index) {
                   vm.listas.arquivosAjuda[$index].posicao = $index;
                    if (angular.isUndefined(item.id)) {
                         if(!vm.servico.campoArquivoAjuda){
                             vm.servico.campoArquivoAjuda = [];
                         }
                         vm.servico.campoArquivoAjuda.push(item);

                    }else{
                        for(var i=0; i<vm.servico.campoArquivoAjuda.length; i++){
                            if(item.id == vm.servico.campoArquivoAjuda[i].id){
                                vm.servico.campoArquivoAjuda[i] = angular.copy(item);
                                return;
                            }
                        }
                    }
                });

                if(!vm.servico.subsecoesDadoComplementar){
                  vm.servico.subsecoesDadoComplementar = [];
                }

                angular.forEach(vm.listas.subsecoesDadoComplementar, function (item, $index) {
                  vm.listas.subsecoesDadoComplementar[$index].posicao = $index;
                  if (angular.isUndefined(item.id)) {
                    vm.servico.subsecoesDadoComplementar.push(item);
                  }else{
                    for(var i=0; i<vm.servico.subsecoesDadoComplementar.length; i++){
                      if(item.id == vm.servico.subsecoesDadoComplementar[i].id){
                        vm.servico.subsecoesDadoComplementar[i] = angular.copy(item);
                        return;
                      }
                    }
                  }
                });

            };

            var habilitaConclusaoTriagem = function () {
              if(angular.isDefined(vm.servico.conclusaoServicoTriagem) &&
                vm.servico.conclusaoServicoTriagem != null){
                vm.concluirServicoTriagem = true;
              }
            };

            vm.validar = function(){
              var valido = vm.form.$valid;
              if (!valido) {
                 servicoService.apresentarCamposInvalidos(vm.form);
              }
              return valido;
            };

            vm.limparPrazo = function(prazo){
              prazo.quantidade = undefined;
              prazo.justificativa = undefined;
            };

            vm.salvar = function () {
                servicoService.formatarPalavrasChaves(vm.servico, vm.palavrasChaves);

                vm.servico.formularioWeb = (vm.servico.formularioWeb === '') ?
                            undefined : vm.servico.formularioWeb;

                vm.servico.assunto = (vm.servico.assunto === '') ?
                            undefined : vm.servico.assunto;

                vm.servico.idArquivo = angular.isUndefined(vm.servico.arquivo) || vm.servico.arquivo == null
                             ? undefined : vm.servico.arquivo.id;

                atualizarServico();
                validarLinkOuFormularioWeb();
                if(vm.servico.prazoAtendimentoAnaliseTecnica.quantidade < vm.servico.prazoAtendimentoTriagem.quantidade){
                  var msg = 'O prazo de análise técnica não pode ser menor que o de triagem.';
                  comumServiceServico.mostrarMensagemError(msg);
                  throw msg;
                }
                validarSolicitantes();
                validarDeclaracoes();

                adicionarServicoGenericoUFAtendimento(vm.servico);

                if(angular.isDefined(vm.servico.unidadeSEI)){
                    vm.servico.grupoUnidadeSEI = [angular.copy(vm.servico.unidadeSEI)];
                }else{
                    vm.servico.grupoUnidadeSEI = undefined;
                }

                vm.servico.idTipoProcessoSei = vm.tipoSei.id;

                servicoService.save(vm.servico).then(function (resposta) {

                    comumServiceServico.mostrarMensagemSucesso('Dados gravados com sucesso');

                    if (!vm.isNew) {
                      getDadosServico(resposta);
                    }

                    $state.go('servicos.SERVICOS_CONSULTAR_SERVICOS');
                    vm.resetServico();

                }, function (erro) {

                    var msg = (erro.data.mensagem) ? erro.data.mensagem[0] : erro.data.error;

                    comumServiceServico.mostrarMensagemError(msg);
                });
            };

            vm.addDocumentos = function ($files, servico, validarExtencao) {
                if(angular.isDefined($files)) {
                  arquivoServiceServicos.addArquivo($files[0], servico, validarExtencao);
                }
            };

            vm.removerArquivo = function (event, id) {
                var confirm = $mdDialog.confirm({focusOnOpen: false})
                    .title($filter('translate')('label-exclusao'))
                    .textContent($filter('translate')('mensagem-confirmar-exclusao'))
                    .targetEvent(event)
                    .ok($filter('translate')('sim'))
                    .cancel($filter('translate')('nao'));

                $mdDialog.show(confirm).then(function () {
                    arquivoServiceServicos.apagarServico(id);
                    vm.servico.arquivo  = {};
                }, function () {
                    $mdDialog.cancel();
                });
            };

            vm.isSemPalavraChave = function(){
                return vm.palavrasChaves.length < 1;
            };

            vm.abrirModalDadosComplementares = function($event, dadoEdicao){
                modalDadosComplementaresFactory
                        .abrirModalDadosComplementares($event, vm.listas.dadosComplementar, vm.servico, dadoEdicao,
                         function(listaDadosComplementar){

                });
            };

              vm.abrirModalArquivosAjuda = function($event, dadoEdicao){
                modalArquivosAjudaFactory
                  .abrirModalArquivosAjuda($event, vm.listas.arquivosAjuda, vm.servico, dadoEdicao,
                    function(listaArquivosAjuda){

                      servicoService.setarOrdem(listaArquivosAjuda);
                      vm.listas.arquivosAjuda = angular.copy(listaArquivosAjuda);

                    });
              };

              vm.$watch('listas.dadosComplementar', function(){
                vm.listas.subsecoesDadoComplementar.forEach(function(subsecao){
                  subsecao.campos.forEach(function(campo, $index){
                    if(campo.removed){
                      subsecao.campos.splice($index, 1);
                    }
                  });
                });
              }, true);

              vm.abrirModalSubsecoes = function($event, dadoEdicao){
                modalSubsecoesFactory
                  .abrirModalSubsecoes($event, vm.listas.dadosComplementar, vm.listas.subsecoesDadoComplementar, vm.servico, dadoEdicao,
                    function(subsecoes){

                });
            };

            vm.clearSearchItem = function() {
                vm.searchItem = '';
            };

            vm.alteraConclusaoTriagem = function(){
                vm.servico.conclusaoServicoTriagem = undefined;
            };

            vm.filtraTipoProcesso = function(filtro) {
              return vm.listTipos.filter(vm.filterTipoProcesso);       
            };

            vm.filterTipoProcesso = function(tipo){
              return tipo.descricao.toUpperCase().indexOf(vm.filtro.toUpperCase()) > -1;
            }

            vm.getTipoProcesso = function(idTipo){              
              if (vm.listTipos){
                return vm.listTipos.find(vm.findTipoProcessoPorId);
              }
            }

            vm.findTipoProcessoPorId = function(tipo){
              if (vm.servico && vm.servico.idTipoProcessoSei || vm.servico && vm.servico.idTipoProcessoSEI)
                return tipo.id==(vm.servico.idTipoProcessoSei || vm.servico && vm.servico.idTipoProcessoSEI);
            }

            init();

        }]);

})();

(function(){
  'use strict';
angular
    .module('su-servicos')
    .controller('detalharServicoGenericoSemRipController',
        ['$scope','$state','$stateParams','$mdDialog','comumServiceServico', 'requerimentoService', 'escopoCompartilhadoService','ComumFactory',
            function ($scope,$state,$stateParams,$mdDialog,comumServiceServico, requerimentoService, escopoCompartilhadoService,ComumFactory) {

                var vm = $scope;
                vm.exibirRepresentanteLegal = ComumFactory.exibirRepresentanteLegal;
                vm.existeImovel = true;

                var init = function() {

                    vm.requerimento = {
                        requerente: []
                    };

                    var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');

                    if(codigoIdentificacao == undefined){
                        $state.go('servicos.triagemRequerimento');
                    }

                    vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};

                    requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(codigoIdentificacao,
                        function (response) {

                            vm.requerimento = response.data.resposta[0];

                            angular.forEach(vm.requerimento.requerente.telefones, function(telefone) {
                                if (telefone.tipoTelefone == "FIXO")
                                    vm.requerimento.requerente.telefoneFixo = telefone.numero;
                                else if (telefone.tipoTelefone == "CELULAR")
                                    vm.requerimento.requerente.telefoneCelular = telefone.numero;
                                else if (telefone.tipoTelefone == "COMERCIAL")
                                    vm.requerimento.requerente.telefoneComercial = telefone.numero;
                            });
                          verificaExistenciaDoImovel(vm.requerimento);
                        });

                    verificarEnderecoCorrespondencia();
                };

                var verificarEnderecoCorrespondencia = function () {
                    if (vm.requerimento.requerente.possuiEnderecoCorrespondencia == true) {
                        vm.requerimento.requerente.enderecoCorrespondencia = angular.copy(vm.requerimento.requerente.endereco);
                    }
                };

                function verificaExistenciaDoImovel (requerimento){

                  if (requerimento.imovel == undefined){
                    vm.existeImovel = false;
                  }else {
                    vm.existeImovel = true;
                  }
              }

                init();

            }]);
})();

(function(){
  'use strict';
angular
    .module('su-servicos')
    .controller('detalharServicoGenericoComRipController',
        ['$scope','$state','$stateParams','$mdDialog','comumServiceServico', 'requerimentoService', 'escopoCompartilhadoService','ComumFactory',
            function ($scope,$state,$stateParams,$mdDialog,comumServiceServico, requerimentoService, escopoCompartilhadoService,ComumFactory) {

                var vm = $scope;
                vm.exibirRepresentanteLegal = ComumFactory.exibirRepresentanteLegal;

                var init = function() {

                    vm.requerimento = {
                        requerente: []
                    };

                    var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');

                    if(codigoIdentificacao == undefined){
                        $state.go('servicos.triagemRequerimento');
                    }

                    vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};

                    requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(codigoIdentificacao,
                        function (response) {

                            vm.requerimento = response.data.resposta[0];

                            angular.forEach(vm.requerimento.requerente.telefones, function(telefone) {
                                if (telefone.tipoTelefone == "FIXO")
                                    vm.requerimento.requerente.telefoneFixo = telefone.numero;
                                else if (telefone.tipoTelefone == "CELULAR")
                                    vm.requerimento.requerente.telefoneCelular = telefone.numero;
                                else if (telefone.tipoTelefone == "COMERCIAL")
                                    vm.requerimento.requerente.telefoneComercial = telefone.numero;
                            });
                        });

                    verificarEnderecoCorrespondencia();
                };

                var verificarEnderecoCorrespondencia = function () {
                    if (vm.requerimento.requerente.possuiEnderecoCorrespondencia == true) {
                        vm.requerimento.requerente.enderecoCorrespondencia = angular.copy(vm.requerimento.requerente.endereco);
                    }
                };

                init();

            }]);
})();

(function () {
    'use strict';
    angular.module('su-servicos')
           .controller('detalharRequerimentoTransferenciaTitularController',
             ['$scope', 'escopoCompartilhadoService','requerimentoService', detalharTransferenciaTitular]);

    function detalharTransferenciaTitular($scope, escopoCompartilhadoService, requerimentoService){

        var vm = $scope;

        var carregarRequerimento = function(id) {
            vm.documento = {
                documentosObrigatorios: [],
                documentosComplementares: [],
                outrosDocumentos: []
            };

            requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(id, function(response) {
                vm.requerimento = response.data.resposta[0];
            });
        };

        vm.possuiRepresentanteLegal = function () {
          try {
            return vm.requerimento.requerente.cpfCnpj.length == 14;
          } catch(error) {
            return false;
          }
        };

        var init = function () {
            var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');
            carregarRequerimento(codigoIdentificacao);
        };

        init();

    }

})();

/**
 * Created by Basis Tecnologia on 21/07/2016.
 */
'use strict';
angular.module('su-servicos')
    .factory('detalharRequerimentoPermissaoUso',['$http', function ($http) {

        var url = '/servicos/api/public/requerimento';

        var findById = function(id){
            return $http.get(url + '/' + id);
        };

        var buscarDadosImovel = function (cpfCnpj, rip) {
            return $http.get(url + '/pesquisar', {params: {cpf_cnpj: cpfCnpj, rip: rip}});
        };


        return {
            findById: findById,
            buscarDadosImovel: buscarDadosImovel
        };
    }]);

(function () {
    'use strict';
  angular.module('su-servicos').controller("detalharSolicitarPermissaoUsoController",
    ['$scope','$state','$stateParams','$mdDialog','detalharRequerimentoPermissaoUso', 'escopoCompartilhadoService',
      'requerimentoService',  'comumServiceServico', function ($scope,$state,$stateParams,$mdDialog,detalharRequerimentoPermissaoUso,
                                                               escopoCompartilhadoService, requerimentoService, comumServiceServico){

      var vm = $scope;

      vm.exibirRepresentanteLegal = comumServiceServico.exibirRepresentanteLegal;

      vm.iniciarAnalise = function(){
        vm.analiseIniciada = true;
      };

      var init = function() {
        vm.requerimento = {
          requerente: []
        };

        var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');
        vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};

        requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(codigoIdentificacao,
          function (response) {

            vm.requerimento = response.data.resposta[0];

            if(vm.requerimento.requerente && vm.requerimento.requerente.telefones){
              angular.forEach(vm.requerimento.requerente.telefones, function(telefone) {
                if (telefone.tipoTelefone == "FIXO")
                  vm.requerimento.requerente.telefoneFixo = telefone.numero;
                else if (telefone.tipoTelefone == "CELULAR")
                  vm.requerimento.requerente.telefoneCelular = telefone.numero;
                else if (telefone.tipoTelefone == "COMERCIAL")
                  vm.requerimento.requerente.telefoneComercial = telefone.numero;
              });
            }

            if (vm.requerimento.requerente && vm.requerimento.requerente.representanteLegal) {
              angular.forEach(vm.requerimento.requerente.representanteLegal.telefones, function(telefone) {
                if (telefone.tipoTelefone == "FIXO")
                  vm.requerimento.requerente.representanteLegal.telefoneFixo = telefone.numero;
                else if (telefone.tipoTelefone == "CELULAR")
                  vm.requerimento.requerente.representanteLegal.telefoneCelular = telefone.numero;
                else if (telefone.tipoTelefone == "COMERCIAL")
                  vm.requerimento.requerente.representanteLegal.telefoneComercial = telefone.numero;
              });
            }

          });

        verificarEnderecoCorrespondencia();
      };

      vm.cancelarRequerimento = function () {
        var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));
        $mdDialog.show({
          controller: vm.cancelarRequerimentoController,
          templateUrl: 'scripts-servicos/pages/requerimento-solicitar-permissao-uso/views/partial/cancelarDialog.html',
          parent: angular.element(document.body),
          targetEvent: event,
          fullscreen: useFullScreen
        })
      };

      vm.cancelarRequerimentoController = function ($scope) {

        if (vm.requerimento && vm.requerimento.justificativa) {
          $scope.justificativaCancelamento = vm.requerimento.justificativa;
        }

        $scope.cancel = function () {
          $mdDialog.cancel();
        };

        $scope.confirmarCancelamento = function () {
          if ($scope.justificativaCancelamento == undefined) {
            comumServiceServico.mostrarMensagemError(comumServiceServico.getMensagemFilter('msg-cancelamento-requerimento'));
          } else {
            vm.requerimento.justificativa = $scope.justificativaCancelamento;
            $mdDialog.show({
              controller: vm.confirmarCancelamentoController,
              templateUrl: 'scripts-servicos/pages/requerimento-solicitar-permissao-uso/views/partial/confirmarCancelamento.html',
              parent: angular.element(document.body),
              targetEvent: event
            })
          }
        };
      };

      var verificarEnderecoCorrespondencia = function () {
        if (vm.requerimento.requerente.possuiEnderecoCorrespondencia == true) {
          vm.requerimento.requerente.enderecoCorrespondencia = angular.copy(vm.requerimento.requerente.endereco);
        }
      };

      vm.editarRequerimento = function () {
        $state.go('servicos.editarRequerimentoIsencao', {
          idRequerimento: vm.idRequerimento, servico: vm.servicoID});
      };

      init();

    }

    ]);
})();

'use strict';
angular.module('su-servicos')
  .factory('detalharRequerimentoRevisaoValorArea', ['$http', function ($http) {

    var url = '/servicos/api/public/requerimento';

    var findById = function (id) {
      return $http.get(url + '/' + id);
    };

    var buscarDadosImovel = function (cpfCnpj, rip) {
      return $http.get(url + '/pesquisar', {params: {cpf_cnpj: cpfCnpj, rip: rip}});
    };

    return {
      findById: findById
    };
  }]);

(function () {
  'use strict';
  angular.module('su-servicos')
    .controller('detalharRequerimentoRevisaoValorAreaController',
    ['$scope', 'escopoCompartilhadoService', 'requerimentoService', 'ComumFactory', detalharRequerimentoRevisaoValorAreaController]);
  function detalharRequerimentoRevisaoValorAreaController($scope, escopoCompartilhadoService , requerimentoService, ComumFactory) {
    var vm = $scope;
    
    vm.exibirRepresentanteLegal = ComumFactory.exibirRepresentanteLegal;

    var carregarRequerimento = function (id) {
      vm.documento = {
        documentosObrigatorios: [],
        documentosComplementares: [],
        outrosDocumentos: []
      };
      requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(id, function (response) {
        vm.requerimento = response.data.resposta[0];
      });
    };

    var init = function () {
      var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');
      carregarRequerimento(codigoIdentificacao);
    };
    init();
  }
})();


'use strict';
angular.module('su-servicos')
  .factory('detalharRequerimentoRevisaoValorArea', ['$http', function ($http) {

    var url = '/servicos/api/public/requerimento';

    var findById = function (id) {
      return $http.get(url + '/' + id);
    };

    return {
      findById: findById
    };
  }]);

(function () {
  'use strict';
  angular.module('su-servicos')
    .controller('detalharRequerimentoRevigoracaoAforamentoController',
    ['$scope', 'escopoCompartilhadoService', 'detalharRequerimentoRevisaoValorArea', 'requerimentoService', detalharRequerimentoRevigoracaoAforamentoController]);
  function detalharRequerimentoRevigoracaoAforamentoController($scope, escopoCompartilhadoService, detalharRequerimentoRevigoracaoAforamentoController , requerimentoService) {
    var vm = $scope;

    var carregarRequerimento = function (id) {
      vm.documento = {
        documentosObrigatorios: [],
        documentosComplementares: [],
        outrosDocumentos: []
      };
      requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(id, function (response) {
        vm.requerimento = response.data.resposta[0];
      });
    };

    var init = function () {
      var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');

      carregarRequerimento(codigoIdentificacao);
    };
    init();
  }
})();


'use strict';
angular.module('su-servicos')
    .factory('detalharRequerimentoRemicaoAforamentoServico',['$http', function ($http) {
        //console.log('Testando')
        var url = '/servicos/api/public/requerimento';

        var findById = function(id){
            return $http.get(url + '/' + id);
        };

        return {
            findById: findById
        };
    }]);

'use strict';
angular
    .module('su-servicos')
    .controller('detalharRequerimentoRemicaoAforamentoController',
    ['$scope','$state','$stateParams','$mdDialog','comumServiceServico','detalharRequerimentoRemicaoAforamentoServico', 'requerimentoService', 'escopoCompartilhadoService',
        function ($scope,$state,$stateParams,$mdDialog,comumServiceServico, detalharRequerimentoRemicaoAforamentoServico, requerimentoService, escopoCompartilhadoService) {

            var vm = $scope;
            vm.exibirRepresentanteLegal = comumServiceServico.exibirRepresentanteLegal;

            var init = function() {
                vm.requerimento = {
                    requerente: [],
                    imovel: {
                        fotosImovel: []
                    }
                };
                
                var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');
                vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};
                requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(codigoIdentificacao,
                    function (response) {

                    vm.requerimento = response.data.resposta[0];
                    vm.idServico = vm.requerimento.idServico;

                    if (vm.requerimento && vm.requerimento.requerente) {
                      angular.forEach(vm.requerimento.requerente.telefones, function(telefone) {
                            if (telefone.tipoTelefone == "FIXO")
                                vm.requerimento.requerente.telefoneFixo = telefone.numero;
                            else if (telefone.tipoTelefone == "CELULAR")
                                vm.requerimento.requerente.telefoneCelular = telefone.numero;
                            else if (telefone.tipoTelefone == "COMERCIAL")
                                vm.requerimento.requerente.telefoneComercial = telefone.numero;
                        });
                    }
                  vm.requerimento.isRemicaoAforamento = true;
                });

                verificarEnderecoCorrespondencia();
            };

            vm.cancelarRequerimento = function () {
                var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));
                $mdDialog.show({
                    controller: vm.cancelarRequerimentoController,
                    templateUrl: 'scripts-servicos/pages/requerimento-isencao/views/partial/cancelarDialog.html',
                    parent: angular.element(document.body),
                    targetEvent: event,
                    fullscreen: useFullScreen
                })
            };

            vm.cancelarRequerimentoController = function ($scope) {

                if (vm.requerimento && vm.requerimento.justificativa) {
                    $scope.justificativaCancelamento = vm.requerimento.justificativa;
                }

                $scope.cancel = function () {
                    $mdDialog.cancel();
                };

                $scope.confirmarCancelamento = function () {
                    if ($scope.justificativaCancelamento == undefined) {
                        comumServiceServico.mostrarMensagemError(comumServiceServico.getMensagemFilter('msg-cancelamento-requerimento'));
                    } else {
                        vm.requerimento.justificativa = $scope.justificativaCancelamento;
                        $mdDialog.show({
                            controller: vm.confirmarCancelamentoController,
                            templateUrl: 'scripts-servicos/pages/requerimento-isencao/views/partial/confirmarCancelamento.html',
                            parent: angular.element(document.body),
                            targetEvent: event
                        })
                    }
                };
            };

            var verificarEnderecoCorrespondencia = function () {
                if (vm.requerimento.requerente.possuiEnderecoCorrespondencia == true) {
                    vm.requerimento.requerente.enderecoCorrespondencia = angular.copy(vm.requerimento.requerente.endereco);
                }
            };

            vm.editarRequerimento = function () {
                $state.go('servicos.editarRequerimentoIsencao', {
                    idRequerimento: vm.idRequerimento, servico: vm.servicoID
                });
            };



            init();

        }]);

'use strict';
angular.module('su-servicos')
    .factory('detalharRequerimentoRegularizacaoUsoServico',['$http', function ($http) {

        var url = '/servicos/api/public/requerimento';

        var findById = function(id){
            return $http.get(url + '/' + id);
        };

        return {
            findById: findById
        };
    }]);

(function(){
  'use strict';
angular
    .module('su-servicos')
    .controller('detalharRequerimentoRegularizacaoUsoController',
    ['$scope','$state','$stateParams','$mdDialog','comumServiceServico','detalharRequerimentoRegularizacaoUsoServico', 'requerimentoService', 'escopoCompartilhadoService','ComumFactory',
        function ($scope,$state,$stateParams,$mdDialog,comumServiceServico, detalharRequerimentoRegularizacaoUsoServico, requerimentoService, escopoCompartilhadoService,ComumFactory) {

            var vm = $scope;

            vm.exibirRepresentanteLegal = ComumFactory.exibirRepresentanteLegal;

            var init = function() {

               var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');

                if(codigoIdentificacao === undefined){
                     $state.go('servicos.triagemRequerimento');
                }

                vm.requerimento = {
                    requerente: [],
                    imovel: {
                        fotosImovel: []
                    }
                };

                vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};

                requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(codigoIdentificacao, function (response) {
                    vm.requerimento = response.data.resposta[0];
                     vm.idServico = vm.requerimento.idServico;

                    if (vm.requerimento && vm.requerimento.requerente) {
                        angular.forEach(vm.requerimento.requerente.telefones, function(telefone) {
                            if (telefone.tipoTelefone == "FIXO")
                                vm.requerimento.requerente.telefoneFixo = telefone.numero;
                            else if (telefone.tipoTelefone == "CELULAR")
                                vm.requerimento.requerente.telefoneCelular = telefone.numero;
                            else if (telefone.tipoTelefone == "COMERCIAL")
                                vm.requerimento.requerente.telefoneComercial = telefone.numero;
                        });
                    }

                });

                verificarEnderecoCorrespondencia();
            };

            vm.cancelarRequerimento = function () {
                var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));
                $mdDialog.show({
                    controller: vm.cancelarRequerimentoController,
                    templateUrl: 'scripts-servicos/pages/requerimento-isencao/views/partial/cancelarDialog.html',
                    parent: angular.element(document.body),
                    targetEvent: event,
                    fullscreen: useFullScreen
                })
            };

            vm.cancelarRequerimentoController = function ($scope) {

                if (vm.requerimento && vm.requerimento.justificativa) {
                    $scope.justificativaCancelamento = vm.requerimento.justificativa;
                }

                $scope.cancel = function () {
                    $mdDialog.cancel();
                };

                $scope.confirmarCancelamento = function () {
                    if ($scope.justificativaCancelamento == undefined) {
                        comumServiceServico.mostrarMensagemError(comumServiceServico.getMensagemFilter('msg-cancelamento-requerimento'));
                    } else {
                        vm.requerimento.justificativa = $scope.justificativaCancelamento;
                        $mdDialog.show({
                            controller: vm.confirmarCancelamentoController,
                            templateUrl: 'scripts-servicos/pages/requerimento-isencao/views/partial/confirmarCancelamento.html',
                            parent: angular.element(document.body),
                            targetEvent: event
                        })
                    }
                };
            };

            var verificarEnderecoCorrespondencia = function () {
                if (vm.requerimento.requerente.possuiEnderecoCorrespondencia == true) {
                    vm.requerimento.requerente.enderecoCorrespondencia = angular.copy(vm.requerimento.requerente.endereco);
                }
            };

            vm.editarRequerimento = function () {
                $state.go('servicos.editarRequerimentoIsencao', {
                    idRequerimento: vm.idRequerimento, servico: vm.servicoID
                });
            };



            vm.verificarReqPossuiInfoSocioEconomicas = function () {
              return requerimentoService.verificarReqPossuiInfoSocioEconomicas(vm.requerimento);
            };

            init();

        }]);
})();

(function () {
    'use strict';

    var controller = function ($scope, $state, comumServiceServico, $stateParams, requerimentoService, escopoCompartilhadoService) {

        var vm = $scope;

        vm.init = function () {
            var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');
            carregarRequerimento(codigoIdentificacao);

        };

        vm.verificarExisteRepresententeLegal = function () {

            if (vm.requerimento && vm.requerimento.requerente) {
                return vm.requerimento.requerente.representanteLegal != undefined;
            }

            return false;
        };

        var carregarRequerimento = function(id) {
            vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};
            requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(id, function (response) {
                vm.requerimento = response.data.resposta[0];
            });
        };

    };

    angular.module('su-servicos')
        .controller('detalharNovaConsultaDominialidadeController',
        ['$scope', '$state', 'comumServiceServico','$stateParams', 'requerimentoService', 'escopoCompartilhadoService', controller]);
})();

(function () {
    'use strict';

    var controller = function ($scope, $state, comumServiceServico, $stateParams, requerimentoService, escopoCompartilhadoService) {

        var vm = $scope;

        vm.ID_OUTROS = 6;

        vm.ID_PESSOA_FISICA = 1;
        vm.ID_PESSOA_JURIDICA = 2;
        vm.ID_CARTORIO = 3;
        vm.ID_AGU = 4;

        vm.init = function () {
            var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');
            carregarRequerimento(codigoIdentificacao);

        };

        vm.verificarExisteRepresententeLegal = function () {

            if (vm.requerimento && vm.requerimento.requerente) {
                return vm.requerimento.requerente.representanteLegal != undefined;
            }

            return false;
        };

        var carregarRequerimento = function(id) {
            vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};
            requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(id, function (response) {
                vm.requerimento = response.data.resposta[0];
            });
        };

    };

    angular.module('su-servicos')
        .controller('detalharNovaConsultaDominialidadeController',
        ['$scope', '$state', 'comumServiceServico','$stateParams', 'requerimentoService', 'escopoCompartilhadoService', controller]);
})();

(function () {
  'use strict';
  angular.module('su-servicos')
    .controller('detalharMudancaRegimeContratoController', ['requerimentoService', 'escopoCompartilhadoService', detalharMudancaRegimeContratoController]);

  function detalharMudancaRegimeContratoController (requerimentoService, escopoCompartilhadoService) {
    var self = this;

    var init = function () {
      var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');
      carregarRequerimento(codigoIdentificacao);
      self.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};
    };

    self.verificarExisteRepresententeLegal = function () {

        if (self.requerimento && self.requerimento.requerente) {
            return vm.requerimento.requerente.representanteLegal != undefined;
        }

        return false;
    };

    var carregarRequerimento = function(id) {
        self.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};
        requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(id, function (response) {
            self.requerimento = response.data.resposta[0];
        });
    };

    self.verificarReqPossuiInfoSocioEconomicas = function () {
      return requerimentoService.verificarReqPossuiInfoSocioEconomicas(self.requerimento);
    };

    init();

  }

})();

'use strict';
angular.module('su-servicos')
    .factory('detalharRequerimentoIsencaoServico',['$http', function ($http) {

        var url = '/servicos/api/public/requerimento';

        var findById = function(id){
            return $http.get(url + '/' + id);
        };

        var buscarDadosImovel = function (cpfCnpj, rip) {
          return $http.get(url + '/pesquisar', {params: {cpf_cnpj: cpfCnpj, rip: rip}});
        };

        return {
            findById: findById,
            buscarDadosImovel: buscarDadosImovel
        };
    }]);

'use strict';
angular
    .module('su-servicos')
    .controller('detalharRequerimentoIsencaoController',
    ['$scope','$state','$stateParams','$mdDialog','comumServiceServico','detalharRequerimentoIsencaoServico', 'escopoCompartilhadoService', 'requerimentoService',
        function ($scope,$state,$stateParams,$mdDialog,comumServiceServico, detalharRequerimentoIsencaoServico, escopoCompartilhadoService, requerimentoService) {

            var vm = $scope;

            var init = function() {
                vm.requerimento = {
                    requerente: []
                };

                var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');
                vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};

                requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(codigoIdentificacao,
                    function (response) {

                    vm.requerimento = response.data.resposta[0];
                    angular.forEach(vm.requerimento.requerente.telefones, function(telefone) {
                        if (telefone.tipoTelefone == "FIXO")
                            vm.requerimento.requerente.telefoneFixo = telefone.numero;
                        else if (telefone.tipoTelefone == "CELULAR")
                            vm.requerimento.requerente.telefoneCelular = telefone.numero;
                        else if (telefone.tipoTelefone == "COMERCIAL")
                            vm.requerimento.requerente.telefoneComercial = telefone.numero;
                    });
                });

                verificarEnderecoCorrespondencia();
            };

            var verificarEnderecoCorrespondencia = function () {
                if (vm.requerimento.requerente.possuiEnderecoCorrespondencia == true) {
                    vm.requerimento.requerente.enderecoCorrespondencia = angular.copy(vm.requerimento.requerente.endereco);
                }
            };

            vm.verificarReqPossuiInfoSocioEconomicas = function () {
              return requerimentoService.verificarReqPossuiInfoSocioEconomicas(vm.requerimento);
            };

            init();

        }]);

(function () {
    'use strict';

    var controller = function ($scope, $state, comumServiceServico, $stateParams, requerimentoService, escopoCompartilhadoService) {

        var vm = $scope;

        vm.init = function () {
            var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');
            carregarRequerimento(codigoIdentificacao);

        };

        vm.verificarExisteRepresententeLegal = function () {

            if (vm.requerimento && vm.requerimento.requerente) {
                return vm.requerimento.requerente.representanteLegal != undefined;
            }

            return false;
        };

        var carregarRequerimento = function(id) {
            vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};
            requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(id, function (response) {
                vm.requerimento = response.data.resposta[0];
            });
        };

    };

    angular.module('su-servicos')
        .controller('detalharConsultaDominialidadeController',
        ['$scope', '$state', 'comumServiceServico','$stateParams', 'requerimentoService', 'escopoCompartilhadoService', controller]);
})();

(function(){
'use strict';
angular
  .module('su-servicos')
  .controller('detalharRequerimentoCancelamentoUtilizacaoController',
  ['$scope','$state','$stateParams','$mdDialog','comumServiceServico','requerimentoService', 'escopoCompartilhadoService','ComumFactory',
    function ($scope,$state,$stateParams,$mdDialog,comumServiceServico, requerimentoService, escopoCompartilhadoService,ComumFactory) {

      var vm = $scope;
      vm.exibirRepresentanteLegal = ComumFactory.exibirRepresentanteLegal;

      var init = function() {

        vm.requerimento = {
          requerente: []
        };

        var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');

        if(codigoIdentificacao == undefined){
          $state.go('servicos.triagemRequerimento');
        }

        vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};

        requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(codigoIdentificacao,
          function (response) {

          vm.requerimento = response.data.resposta[0];

          angular.forEach(vm.requerimento.requerente.telefones, function(telefone) {
            if (telefone.tipoTelefone == "FIXO")
              vm.requerimento.requerente.telefoneFixo = telefone.numero;
            else if (telefone.tipoTelefone == "CELULAR")
              vm.requerimento.requerente.telefoneCelular = telefone.numero;
            else if (telefone.tipoTelefone == "COMERCIAL")
              vm.requerimento.requerente.telefoneComercial = telefone.numero;
          });
        });

        verificarEnderecoCorrespondencia();
      };

      var verificarEnderecoCorrespondencia = function () {
        if (vm.requerimento.requerente.possuiEnderecoCorrespondencia == true) {
          vm.requerimento.requerente.enderecoCorrespondencia = angular.copy(vm.requerimento.requerente.endereco);
        }
      };

      init();

    }]);
})();

'use strict';
angular
  .module('su-servicos')
  .controller('detalharRequerimentoCancelamentoCobrancaController',
  ['$scope','$state','$stateParams','$mdDialog','comumServiceServico', 'requerimentoService', 'escopoCompartilhadoService',
    function ($scope,$state,$stateParams,$mdDialog,comumServiceServico, requerimentoService, escopoCompartilhadoService) {

      var vm = $scope;
      vm.exibirRepresentanteLegal = comumServiceServico.exibirRepresentanteLegal;

      var init = function() {
        vm.requerimento = {
          requerente: []
        };
        
        var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');

        vm.documento = {documentosObrigatorios: [], documentosComplementares: [], outrosDocumentos: []};

        requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(codigoIdentificacao,
           function (response) {

          vm.requerimento = response.data.resposta[0];
          vm.idServico = vm.requerimento.idServico;

          if (vm.requerimento && vm.requerimento.requerente) {
            angular.forEach(vm.requerimento.requerente.telefones, function(telefone) {
              if (telefone.tipoTelefone == "FIXO")
                vm.requerimento.requerente.telefoneFixo = telefone.numero;
              else if (telefone.tipoTelefone == "CELULAR")
                vm.requerimento.requerente.telefoneCelular = telefone.numero;
              else if (telefone.tipoTelefone == "COMERCIAL")
                vm.requerimento.requerente.telefoneComercial = telefone.numero;
            });
          }

        });

        verificarEnderecoCorrespondencia();
      };

      vm.cancelarRequerimento = function () {
        var useFullScreen = ($mdMedia('sm') || $mdMedia('xs'));
        $mdDialog.show({
          controller: vm.cancelarRequerimentoController,
          templateUrl: 'scripts-servicos/pages/requerimento-isencao/views/partial/cancelarDialog.html',
          parent: angular.element(document.body),
          targetEvent: event,
          fullscreen: useFullScreen
        })
      };

      vm.cancelarRequerimentoController = function ($scope) {

        if (vm.requerimento && vm.requerimento.justificativa) {
          $scope.justificativaCancelamento = vm.requerimento.justificativa;
        }

        $scope.cancel = function () {
          $mdDialog.cancel();
        };

        $scope.confirmarCancelamento = function () {
          if ($scope.justificativaCancelamento == undefined) {
            comumServiceServico.mostrarMensagemError(comumServiceServico.getMensagemFilter('msg-cancelamento-requerimento'));
          } else {
            vm.requerimento.justificativa = $scope.justificativaCancelamento;
            $mdDialog.show({
              controller: vm.confirmarCancelamentoController,
              templateUrl: 'scripts-servicos/pages/requerimento-isencao/views/partial/confirmarCancelamento.html',
              parent: angular.element(document.body),
              targetEvent: event
            })
          }
        };
      };

      var verificarEnderecoCorrespondencia = function () {
        if (vm.requerimento.requerente.possuiEnderecoCorrespondencia == true) {
          vm.requerimento.requerente.enderecoCorrespondencia = angular.copy(vm.requerimento.requerente.endereco);
        }
      };



      init();

    }]);

'use strict';
angular.module('su-servicos')
  .factory('detalharRequerimentoAutorizacaoObraServico',['$http', function ($http) {

    var url = '/servicos/api/public/requerimento';

    var findById = function(id){
      return $http.get(url + '/' + id);
    };

    var buscarDadosImovel = function (cpfCnpj, rip) {
      return $http.get(url + '/pesquisar', {params: {cpf_cnpj: cpfCnpj, rip: rip}});
    };


    return {
      findById: findById,
      buscarDadosImovel: buscarDadosImovel
    };
  }]);

(function () {

  'use strict';
  angular
    .module('su-servicos')
    .controller('detalharRequerimentoAutorizacaoObraController',
                ['$scope','$state','$stateParams','$mdDialog','comumServiceServico',
                'requerimentoService', 'escopoCompartilhadoService',

      function ($scope,$state,$stateParams,$mdDialog, comumServiceServico, requerimentoService,
                escopoCompartilhadoService) {

        var vm = $scope;
        vm.exibirRepresentanteLegal = comumServiceServico.exibirRepresentanteLegal;

        var init = function () {

          var codigoIdentificacao = escopoCompartilhadoService
                        .getObjetoDefined('codigoIdentificacao', 'servicos.SERVICOS_TRIAGEM');

          requerimentoService.recuperarRequerimentoPorCodigoIdentificacao(codigoIdentificacao,
              function (response) {

                  vm.requerimento = response.data.resposta[0];
          });

        };


        init();
      }]);

})();


(function () {

    'use strict';
    angular
        .module('su-servicos')
        .controller('portalRedirecionamentoController', ['$location', '$window', '$scope', function ($location, $window, $scope) {

            const urlParam = $location.search().url;
            const decodedUrl = decodeURIComponent(urlParam);

            $scope.hasValidUrl = decodedUrl && isValidUrl(decodedUrl);

            if ($scope.hasValidUrl) {
                console.log('Redirecionando para: ', decodedUrl, '')
                $window.location.href = decodedUrl;
            } else {
                console.log('SEM URL Redirecionamento:', urlParam);
            }

            function isValidUrl(url) {
                return /^(http|https):\/\/[^ "]+$/.test(url);
            }
        }]);

})();

(function() {

  'use strict';

  angular
    .module('su-servicos')
    .factory('grupoProcessoSeiService', ['$http', 'comumServiceServico', '$filter',
      function ($http, comumServiceServico, $filter) {

        var url = '/servicos/api/grupo-sei';

        var findById = function (id) {
          return $http.get(url + '/' + id);
        };

        var save = function (grupo) {
          if (grupo.id) {
            return $http.put(url, grupo);
          } else {
            return $http.post(url, grupo);
          }
        };

        var deletar = function (id) {
          return $http.delete(url + '/' + id);
        };

        var findAll = function (offset, limit) {
          return $http.get(url, {
            params: {
              offset: offset,
              limit: limit
            }
          });
        };

        var pesquisarTituloGrupo = function () {
          return $http.get(url + '/pesquisar-titulo-grupo');
        };

        var filterFindAll = function (nome, situacao, offset, limit) {
          nome = nome != undefined ? nome : "";
          return $http.get(url, {
            params: {
              nome: nome,
              situacao: situacao,
              offset: offset,
              limit: limit
            }
          });
        };

        var listarGruposUnidadeSei = function () {
          return $http.get(url + '/listar');
        };

        var validarMinLength = function (model, msg) {
          var texto = comumServiceServico.htmlToPlaintext(model);

          if (texto.length < 10) {
            comumServiceServico.mostrarMensagemError($filter('translate')('error-min-caracteres', {
              max: '10'
            }));
            return false;
          }

          return true;
        };

        var setarOrdem = function (lista) {
          angular.forEach(lista, function (item, $index) {
            item.ordem = $index;
          });
        };

        var ordenaLista = function (atual, destino, lista) {
          var item = lista[atual];
          lista[atual] = lista[destino];
          lista[destino] = item;
        };

        var verificarExisteCampo = function (mensagens, campo) {
          var existe = false;
          angular.forEach(mensagens, function (item) {
            if (item == campo)
              existe = true;
          });
          return existe;
        };

        var apresentarCamposInvalidos = function (form) {

          var error = form.$error;
          var mensagens = [];
          angular.forEach(error.required, function (field) {
            if (field.$invalid) {
              if (!verificarExisteCampo(mensagens, field.$name))
                mensagens.push(field.$name);
            }
          });

          if (mensagens.length > 0) {
                comumServiceServico.mostrarMensagemError('Necessário preencher os campos obrigatórios');
          }
        };

        var getQueryDefault = function () {
            return angular.copy({
              order: 'tipo',
              limit: 10,
              limitsPage: [5, 10, 20],
              page: 1
            });
        };

        var getQueryNome= function () {
          return angular.copy({
            order: 'nome',
            limit: 10,
            limitsPage: [5, 10, 20],
            page: 1
          });
        };

        var getUfsDisponiveis = function (idGrupoUnidadeSei) {
          return $http.get(url + '/ufs-disponiveis/' + idGrupoUnidadeSei);
        };

        return {
          findById: findById,
          save: save,
          deletar: deletar,
          findAll: findAll,
          pesquisarTituloGrupo: pesquisarTituloGrupo,
          filterFindAll: filterFindAll,
          listarGruposUnidadeSei: listarGruposUnidadeSei,
          validarMinLength: validarMinLength,
          setarOrdem: setarOrdem,
          ordenaLista: ordenaLista,
          verificarExisteCampo: verificarExisteCampo,
          apresentarCamposInvalidos: apresentarCamposInvalidos,
          getQueryDefault: getQueryDefault,
          getQueryNome: getQueryNome,
          getUfsDisponiveis: getUfsDisponiveis
        };
      }])

})();

(function(){

    'use strict';

    angular
        .module('su-servicos')
        .controller('grupoProcessoSeiListController',  ['$scope','grupoProcessoSeiService','$state', '$mdDialog','comumServiceServico',
                    '$mdToast',
        function ($scope, grupoProcessoSeiService, $state, $mdDialog,comumServiceServico,
                  $mdToast) {

        var init = function() {
            $scope.grupos = [];
            $scope.minDate = new Date();
            $scope.registroInicial = 1;
            $scope.tamanhoLimite = 5;
            $scope.totalItems = 0;
            $scope.situacao="todos";
            $scope.filterFindAll();            
        };

        /*$scope.updateTable = function(){
            $scope.carregando = true;
            grupoProcessoSeiService.findAll(($scope.registroInicial - 1), $scope.tamanhoLimite).then(function (resp) {
                $scope.grupos = resp.data.resposta[0].content;
                $scope.totalItems = resp.data.resposta[0].totalElements;
            });
        };*/

        $scope.filterFindAll = function() {
            grupoProcessoSeiService.filterFindAll($scope.descricao, $scope.situacao, ($scope.registroInicial - 1), $scope.tamanhoLimite).then(function(resp) {
                $scope.grupos = resp.data.resposta[0].content;
                $scope.totalItems = resp.data.resposta[0].totalElements;
            });
        };

        $scope.findPagination = function() {
            $scope.filterFindAll();
           /* if ( ($scope.descricao != undefined && $scope.descricao != "") ||
            ($scope.situacao != undefined && $scope.situacao != "") ) {
                $scope.filterFindAll();
            } else {
                $scope.updateTable();
            }*/
        };

        $scope.adicionarGrupo = function(e){
          e.preventDefault();
            $state.go('servicos.SERVICOS_CADASTRAR_GRUPO_PROCESSO_SEI');
        };

        $scope.clearFilter = function() {
            $scope.descricao = "";
            $scope.situacao = "todos";
        };

        $scope.showConfirmExcluir = function(ev, id) {
            var confirm = $mdDialog.confirm({focusOnOpen:false})
                .title('Excluir Grupo Processo Sei')
                .textContent('Deseja excluir dados?')
                .targetEvent(ev)
                .ok('Sim')
                .cancel('Não');
            $mdDialog.show(confirm).then(function() {
                grupoProcessoSeiService.deletar(id).then(function(resposta){
                    $scope.filterFindAll();
                }, function (erro) {

                    var msg = (erro.data.mensagem) ? erro.data.mensagem[0] : erro.data.error;

                    comumServiceServico.mostrarMensagemError(msg);
                });
            }, function() {
                $mdDialog.cancel();
            });
        };

        $scope.novoCadastro = function() {
            $state.go('servicos.SERVICOS_CADASTRAR_GRUPO_PROCESSO_SEI');
        };

        init();

    }]);

})();

(function(){

    'use strict';

    angular
        .module('su-servicos')
        .controller('detalharGrupoProcessoSeiController',
                      ['$scope', '$stateParams', '$state', 'grupoProcessoSeiService', 
                      'comumServiceServico', '$filter','servicoService','unidadeSeiService',
                      '$element',
            function ($scope, $stateParams, $state, grupoProcessoSeiService, 
                      comumServiceServico, $filter, servicoService,unidadeSeiService,
                      $element) {

            $element.find('input').on('keydown', function(ev) {
                ev.stopPropagation();
            });

            var vm = $scope;
            vm.isNew = !($stateParams.id);

            var init = function () {

                vm.selected = [];

                vm.tituloStatico = $filter('translate')('label-titulo-detalhar');

                vm.registroInicial = 1;
                vm.tamanhoLimite = 5;
                vm.totalItems = 0;

                grupoProcessoSeiService.findById($stateParams.id).then(function (resposta) {

                    getDados(resposta);

                });   
                
                servicoService.pesquisarPorGrupoUnidadeSei($stateParams.id).then(function (resposta) {
                  $scope.listServicos = resposta.data.resposta;
                });

                /*unidadeSeiService.pesquisarPorGrupoUnidadeSei($stateParams.id).then(function (resposta) {
                  $scope.listUnidades = resposta.data.resposta;
                });*/
                getUnidades();

            };

            var getUnidades = function(){
              unidadeSeiService.unidadesPorGrupoPaginado($stateParams.id,($scope.registroInicial - 1), $scope.tamanhoLimite).then(function (resposta) {
                vm.listUnidades = resposta.data.resposta[0].content;
                vm.totalItems = resposta.data.resposta[0].totalElements;
              });
            }

            var getDados = function (resposta) {
                vm.grupo = resposta.data.resposta[0];

            };           
          
            vm.mostrarResultado = function (box, num_max, campospan) {
              document.getElementById(campospan).innerHTML = box.length + " / 5";
            };

            vm.findPagination = function(page, limit) {
              $scope.registroInicial = page;
              $scope.tamanhoLimite = limit;
              getUnidades();
            };

            function indexOf(lista, elemento) {
              for (var i = 0; i < lista.length; i++) {
                if (lista[i].id === elemento.id) {
                   return i;
                }
              }
            }

            init();

        }]);

})();

(function(){

    'use strict';

    angular
        .module('su-servicos')
        .controller('cadastrarGrupoProcessoSeiController',
                      ['$scope', '$stateParams', '$state', 'grupoProcessoSeiService', 'unidadeSeiService',
                      'comumServiceServico', '$filter',
                      '$element','$mdDialog',
            function ($scope, $stateParams, $state, grupoProcessoSeiService, unidadeSeiService,
                      comumServiceServico, $filter, 
                      $element,$mdDialog) {

            $element.find('input').on('keydown', function(ev) {
                ev.stopPropagation();
            });

            var vm = $scope;
            vm.isNew = !($stateParams.id);
            
            var init = function () {

                $scope.registroInicial = 1;
                $scope.tamanhoLimite = 5;
                $scope.totalItems = 0;

                if (vm.isNew) {
                  vm.titulo = $filter('translate')('label-titulo-cadastrar');
                    vm.reset();
                } else {
                  vm.idGrupo = $stateParams.id;
                    vm.titulo = $filter('translate')('label-titulo-editar');
                    grupoProcessoSeiService.findById(vm.idGrupo).then(function (resposta) {

                        getDados(resposta);
                        getUnidades();

                    });                    
                    vm.getUfsDisponiveis();
                }
                
            };

            var getDados = function (resposta) {
                vm.grupo = resposta.data.resposta[0];
                vm.idGrupo = vm.grupo.id;
            };

            var getUnidades = function(){
              $scope.carregando = true;
              unidadeSeiService.unidadesPorGrupoPaginado(vm.idGrupo,($scope.registroInicial - 1), $scope.tamanhoLimite).then(function (resposta) {
                vm.listUnidades = resposta.data.resposta[0].content;
                vm.totalItems = resposta.data.resposta[0].totalElements;
              });
            }

            vm.reset = function () {
                vm.grupo = {
                  descricao: ''
                };

                if (angular.isDefined(vm.manterGrupoProcessoSei)) {
                    vm.manterGrupoProcessoSei.$setUntouched();
                }
            };

            vm.mostrarResultado = function (box, num_max, campospan) {
              document.getElementById(campospan).innerHTML = box.length + " / 5";
            };

            function indexOf(lista, elemento) {
              for (var i = 0; i < lista.length; i++) {
                if (lista[i].id === elemento.id) {
                   return i;
                }
              }
            }

            vm.validar = function(){
              var valido = vm.form.$valid;
              if (!valido) {
                 servicoService.apresentarCamposInvalidos(vm.form);
              }
              return valido;
            };

            vm.salvar = function () {
              $scope.carregando = true;
              grupoProcessoSeiService.save(vm.grupo).then(function (resposta) {
                    comumServiceServico.mostrarMensagemSucesso('Dados gravados com sucesso'); 
                    getDados(resposta);
                    getUnidades();
                    vm.getUfsDisponiveis();
                    vm.titulo = $filter('translate')('label-titulo-editar');
                }, function (erro) {

                    var msg = (erro.data.mensagem) ? erro.data.mensagem[0] : erro.data.error;

                    comumServiceServico.mostrarMensagemError(msg);
                });
            };

            vm.clearSearchItem = function() {
                vm.searchItem = '';
            };

            vm.findPagination = function(page, limit) {
              $scope.registroInicial = page;
              $scope.tamanhoLimite = limit;
              getUnidades()
            };

            vm.novaUnidade = function(){
              vm.unidadeSei = {uf:'0'};
              vm.getUfsDisponiveis();
            }

            vm.salvarUnidade = function(){
              $scope.carregando = true;
              vm.unidadeSei.idGrupoUnidadeSei = vm.idGrupo;
              unidadeSeiService.save(vm.unidadeSei).then(function (resposta) {
                comumServiceServico.mostrarMensagemSucesso('Dados gravados com sucesso');  
                vm.unidadeSei = null;
                vm.editandoUnidade = false;
                getUnidades();  
                vm.getUfsDisponiveis();          
              }, function (erro) {
                  var msg = (erro.data.mensagem) ? erro.data.mensagem[0] : erro.data.error;
                  comumServiceServico.mostrarMensagemError(msg);
              });
                
            }

            vm.getUfsDisponiveis = function(item){
              $scope.carregando = true;
              grupoProcessoSeiService.getUfsDisponiveis(vm.idGrupo).then(function (resposta) {
                vm.listaUfsDisponiveis = resposta.data.resposta;
                if (vm.listaUfsDisponiveis!= null && vm.listaUfsDisponiveis.length>0){
                    vm.msgAlerta = 'Atenção: Esse grupo não tem unidades SEI cadastradas para todas as UF’s. Isso pode inviabilizar a integração com o SEI para requerimentos de serviços que estejam vinculados a esse grupo'
                } else {
                    vm.msgAlerta ='';
                }
                if (item){
                  vm.listaUfsDisponiveis.unshift(item.uf);
                }
                if (!vm.editandoUnidade){
                  vm.listaUfsDisponiveis.unshift("Todas");
                }
              });
            }

            vm.getSiglasUnidade = function(item){
              $scope.carregando = true;
              unidadeSeiService.getSiglasUnidadesPorUf(vm.unidadeSei.uf).then(function (resposta) {
                vm.listaSiglas = resposta.data.resposta;
                if (!item){
                  vm.unidadeSei.codigoSei = "";
                  vm.unidadeSei.sigla = "0";
                }
                
              });
            }

            vm.filtrarPelaSigla = function(unit){
             return unit.sigla === vm.unidadeSei.sigla;
            }

            vm.setaCodigoUnidade = function(){
              var unidadeCodigo = vm.listaSiglas.find(vm.filtrarPelaSigla);
              //var unidadeCodigo = vm.listaSiglas.find(sig => sig.sigla===vm.unidadeSei.sigla );
              if(unidadeCodigo){
                vm.unidadeSei.codigoSei = unidadeCodigo.codigoSei;
              }
            }

            vm.showConfirmExcluir = function(ev, id) {
              var confirm = $mdDialog.confirm({focusOnOpen:false})
                  .title('Excluir Unidade Sei')
                  .textContent('Deseja excluir dados?')
                  .targetEvent(ev)
                  .ok('Sim')
                  .cancel('Não');
              $mdDialog.show(confirm).then(function() {
                  unidadeSeiService.deletar(id).then(function(resposta){
                    getUnidades(); 
                    vm.getUfsDisponiveis(); 
                  });
              }, function() {
                  $mdDialog.cancel();
              });
            };

            vm.alterarUnidade = function(ev, item){
              vm.unidadeSei = item;
              vm.getUfsDisponiveis(item);//Precisa adicionar o atual   
              vm.getSiglasUnidade(item);
              vm.editandoUnidade = true;              
            }

            vm.cancelar = function(ev, item){
              vm.unidadeSei = null;
              vm.editandoUnidade = false;
              getUnidades(); 
            }

            init();

        }]);

})();
/**
 * Created by Basis Tecnologia on 19/05/2016.
 */
'use strict';
angular.module('su-servicos').factory('DominioService', ["$resource", function ($resource) {
    return $resource('/servicos/api/dominio',null, {
        tipoSegmento: {
            method: 'GET',
            isArray:true,
            url: '/servicos/api/dominio/tipo-segmento/all'
        }
    });
}]);

'use strict';
angular.module('su-servicos')
    .factory('analiseSimplificadaService', ['$http', function ($http) {

        var url = '/servicos/api/analiseRequerimento';

        var salvar = function (analiseRequerimento, idRequerimento) {
            return $http.post(url + '/salvar/' + idRequerimento, analiseRequerimento);
        };

        var buscarPorRequerimentoId = function (requerimentoId) {
            return $http.get(url + "/findByRequerimento/" + requerimentoId);
        };

        var buscarAnaliseRequerimentoId = function (requerimentoId) {
            return $http.get(url + "/findByAnaliseRequerimento/" + requerimentoId);
        };

        var buscarPendentesPorRequerimentoId = function (requerimentoId) {
            return $http.get(url + "/findPendentesByRequerimento/" + requerimentoId);
        };

        var buscarHistoricoPorRequerimentoId = function (offset, limit, requerimentoId) {
            return $http.get("/servicos/api/historico/findByRequerimento/" + requerimentoId + "?offset=" + offset + "&limit=" + limit);
        };

        return {
            salvar: salvar,
            buscarPorRequerimentoId: buscarPorRequerimentoId,
            buscarHistoricoPorRequerimentoId: buscarHistoricoPorRequerimentoId,
            buscarPendentesPorRequerimentoId: buscarPendentesPorRequerimentoId,
            buscarAnaliseRequerimentoId: buscarAnaliseRequerimentoId
        }
    }]);

(function () {
    'use strict';

    angular
        .module('su-servicos')
        .controller('analiseTecnicaSimplificadaController',
            ['$scope', '$state', 'comumServiceServico', '$stateParams', controller]);

    function controller($scope, $state, comumServiceServico, $stateParams) {

        var vm = $scope;
        vm.idRequerimento = $stateParams.idRequerimento;

    }

})();



(function () {
    "uses-strict"
    var directive = function ($mdDialog) {
        return ({
            link: function link($scope, element) {
                element.on('click', function () {

                    if (element[0].currentSrc && element[0].currentSrc != '') {
                        var imagens =
                        $mdDialog.show({
                            controller: ['$scope', function ($scope) {

                                $scope.imagem = element[0].currentSrc;
                                $scope.fechar = function () {
                                    $mdDialog.cancel();
                                };

                            }],
                            templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/visualizarImagem.html',
                            parent: angular.element(document.body),
                            clickOutsideToClose: false
                        });
                    }

                });



            },
            restrict: 'A'
        });
    };

    angular.module('su-servicos')
        .directive('visualizarImagem',['$mdDialog', directive]);

})();

/**
 * Created by thiago on 14-07-2017.
 */

(function () {
  'use strict';

  angular.module('su-servicos')
    .directive('servicoTrim', directive);

  function directive() {
    return {
      require: 'ngModel',
      link: function (scope, element, attrs, modelCtrl) {
        modelCtrl.$formatters.push(function (value) {

          if (!angular.isString.trim()(value)) {
            return value;
          }
            return value.replace(/[\s]/g, '');
        });
      }
    };
  }
})();

(function () {

  var directive = function (comumServiceServico, arquivoServiceServicos, $mdDialog, $http) {
    return {
      restrict: 'A', //E = element, A = attribute, C = class, M = comment
      scope: {
        modalDocumento: '=',
        modalIdRequerimento: '='
      },
      link: function ($scope, element, attrs) {

        var POSSUO = 'POSSUO';
        var NAO_POSSUO = 'NAO_POSSUO';
        var POSSO_CONSEGUIR = 'POSSO_CONSEGUIR';

        $scope.$watch([$scope.modalDocumento, $scope.modalIdRequerimento], function(){
          if ($scope.modalDocumento && $scope.modalIdRequerimento) {
            getDocumentosSalvos();
          }
        });

        var getDocumentosSalvos = function () {
          $http.get('servicos/api/public/arquivo-requerimento/requerimento/' + $scope.modalIdRequerimento).then(function (response) {
              var arquivos = response.data.resposta;
              carregarDocumentos(arquivos, $scope.modalDocumento);
            });
        };

        var carregarDocumentos = function(arquivos, documento) {
            if(arquivos) {
              documento.files = [];
              angular.forEach(arquivos, function (arquivo) {
                if (arquivo.idDocumento == documento.id) {
                  documento.situacaoDocumentoComplementar = arquivo.situacaoDocumentoComplementar;
                  if (arquivo.situacaoDocumentoComplementar == NAO_POSSUO
                    || arquivo.situacaoDocumentoComplementar == POSSO_CONSEGUIR) {
                    documento.idRequerimentoArquivo = arquivo.id;
                  } else {
                    documento.files.push({id: arquivo.id, idArquivo: arquivo.idArquivo, name: arquivo.name, naoRemover : true});
                  }
                }
              });
            }
        };

        $scope.abrirListaDocumentos = function () {
          var documento = $scope.modalDocumento;
          if(documento.files.length === 1 && documento.files[0].idArquivo){
            location.href = arquivoServiceServicos.download(documento.files[0].idArquivo);
            return;
          }
          $mdDialog.show({
            controller: ['$scope', function ($scope) {
              $scope.documento = documento;
              $scope.detalhar = true;
              $scope.baixar = function (id) {
                return arquivoServiceServicos.download(id);
              };

              $scope.fechar = function () {
                $mdDialog.cancel();
              };
            }],
            templateUrl: 'scripts-servicos/app/directives/partials/modalDocumentos.html',
            parent: angular.element(document.body),
            clickOutsideToClose: false
          });
        };

        element.bind('click', $scope.abrirListaDocumentos);

      }
    };
  };

  angular.module('su-servicos')
    .directive('modalDocumento',['comumServiceServico', 'arquivoServiceServicos', '$mdDialog', '$http', directive]);

})();


 //http://weblogs.asp.net/dwahlin/creating-custom-angularjs-directives-part-i-the-fundamentals

 (function () {

    var directive = function ($http) {
        return {
            restrict: 'EA', //E = element, A = attribute, C = class, M = comment
            scope: {
                //@ reads the attribute value, = provides two-way binding, & works with functions
                servico: '=ngModel',
                idServico: '='
            },
            templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/informacaoServico.html',
            //controller: controllerFunction, //Embed a custom controller in the directive
            link: function ($scope, element, attrs) {

                $http.get('servicos/api/public/servico/' + $scope.idServico).then(function (resposta) {

                    if (resposta.data.resposta) {
                        $scope.servico = resposta.data.resposta[0];
                    }
                });

            } //DOM manipulation
        };
    };

    angular.module('su-servicos')
        .directive('informacaoServico',[ '$http', directive]);

})();

(function () {

    var directive = function (comumServiceServico, arquivoServiceServicos, $filter, $mdDialog, $q) {
        return {
            restrict: 'EA', //E = element, A = attribute, C = class, M = comment
            scope: {
                //@ reads the attribute value, = provides two-way binding, & works with functions
                imovel: '=',
                visualizar: '='
            },
            templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/fotosImovel.html',
            //controller: controllerFunction, //Embed a custom controller in the directive
            link: function (vm, element, attrs) {

                vm.listaFotos = [];
                vm.previewFoto = [];

                var carregarFotos = true;

                var isFotoJaPossuiUpload = function(foto){
                     return foto.id != undefined
                            && foto.base64 != undefined;
                }

                vm.$watch('imovel.fotosImovel', function(newValue){

                    if(newValue && carregarFotos){

                        angular.forEach(vm.imovel.fotosImovel, function (foto) {

                            if(isFotoJaPossuiUpload(foto)){
                                angular.extend(foto, {
                                    name : foto.nome,
                                    type: 'image/' + foto.extencao.replace('.', '')
                                })

                                vm.listaFotos.push(foto);
                                vm.previewFoto.push(foto.base64);
                                carregarFotos = false;
                                foto.isUploaded = true;
                            }

                        });
                    }
                });


                var adicionarFotosImovel = function(resultadoUpload){

                    var uploaded = [];

                    angular.forEach(vm.imovel.fotosImovel, function(foto){
                        if(foto.isUploaded){
                            uploaded.push(foto);
                        }
                    });

                    vm.imovel.fotosImovel = angular.copy(uploaded);

                    angular.forEach(resultadoUpload, function(res){
                             vm.imovel.fotosImovel.push(
                                     {id: res.data.resposta[0]});
                     });

                };


                var upload = function(listaFotos, previewFoto, callback){

                    if(validarFotos(listaFotos)){

                        vm.previewFoto = [];
                        vm.imovel.fotosImovel = [];
                        vm.listaFotos = [];

                        var promisses = [];

                        angular.forEach(previewFoto, function(preview){
                            vm.previewFoto.push(preview);
                        });

                        angular.forEach(listaFotos, function(foto){
                            vm.listaFotos.push(foto);

                            if(!isFotoJaPossuiUpload(foto)){
                                promisses.push(arquivoServiceServicos.uploadArquivo(foto));
                            }
                        });

                        if(promisses.length > 0){
                            $q.all(promisses).then(adicionarFotosImovel);
                        }

                        callback();
                    }
                };



                var validarFotos = function(listaFotos){

                    if(!listaFotos){
                        return true;
                    }

                    if (listaFotos.length > 4) {
                        comumServiceServico.mostrarMensagemError(
                            comumServiceServico.getMensagemFilter('maximo-foto'), 'html');
                        return false;
                    }

                    for(var i=0;i <listaFotos.length; i++){
                        if(arquivoServiceServicos.validarExtensaoImagem(listaFotos[i])){

                            comumServiceServico.mostrarMensagemError(
                                comumServiceServico.getMensagemFilter('formato-invalido'), 'html');
                                return false
                        }
                    }

                    return true;
                };




                vm.abrirModalUploadFotos = function(ev, apagar, fotos, preview) {

                    $mdDialog.show({
                        controller: ['$scope', function($scope){

                            if(!apagar){

                                $scope.listaFotos = [];
                                $scope.previewFoto = [];

                                angular.forEach(vm.previewFoto, function(preview){
                                        $scope.previewFoto.push(preview);
                                });

                                angular.forEach(vm.listaFotos, function(foto){
                                    $scope.listaFotos.push(foto);
                                });

                            }else{

                                $scope.listaFotos = (fotos) ? fotos : [];
                                $scope.previewFoto = (preview) ? preview : [];
                            }

                            $scope.fechar = function() {
                                $scope.listaFotos = [];
                                $scope.previewFoto = [];
                                $mdDialog.cancel();
                            };

                            $scope.setFotosImovel = function (fotos) {

                                angular.forEach(fotos, function(foto){

                                    var exists = false;

                                    angular.forEach($scope.listaFotos, function(item){
                                        if(foto.name ==item.name)
                                            exists = true;
                                    });

                                    if(!exists && !isFotoJaPossuiUpload(foto)){

                                        comumServiceServico.gerarPreviewImagens(foto, function (src) {
                                            $scope.listaFotos.push(foto);
                                            $scope.previewFoto.push(src);
                                        });

                                    }

                                });
                            };

                            $scope.removerFotoImovel = function(event, index) {

                                var dialog = $mdDialog;

                                var manterModalAberto = function(){
                                     vm.abrirModalUploadFotos(ev, true,
                                     $scope.listaFotos, $scope.previewFoto);
                                };

                                var fechar = function(){
                                    manterModalAberto();
                                    dialog.hide();
                                    dialog = undefined;
                                };

                                var confirm = dialog.confirm({focusOnOpen: false})
                                    .content("Deseja remover esta foto?")
                                    .ariaLabel("Excluir Foto de Imóvel")
                                    .cancel("Não")
                                    .ok("Sim");

                                dialog.show(confirm).then(function (){

                                    if(vm.imovel.fotosImovel[index].isUploaded){
                                        vm.imovel.fotosImovel.splice(index, 1);
                                    }

                                    $scope.listaFotos.splice(index, 1);
                                    $scope.previewFoto.splice(index, 1);

                                    fechar();

                                }, function(){
                                    fechar();
                                });

                                manterModalAberto();
                            };

                            $scope.open = function(index){
                                var image = new Image();
                                image.src = $scope.previewFoto[index];
                                var w = window.open("");
                                w.document.write(image.outerHTML);
                            };

                            $scope.confirmarUploadFotosImovel = function() {
                                upload($scope.listaFotos, $scope.previewFoto,
                                         function(){
                                        $mdDialog.hide();
                                });
                            };
                        }],
                        templateUrl: 'scripts-servicos/app/directives/partials/modalFotosImovel.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose:false,
                        locals:{
                        }
                    }).then(function(){
                    });
            };
            }
        };
    };

    angular.module('su-servicos')
        .directive('fotosImovel',['comumServiceServico', 'arquivoServiceServicos', '$filter',
                   '$mdDialog','$q', directive]);

})();

(function () {

  var directive = function (comumServiceServico, arquivoServiceServicos) {
        return {
            restrict: 'EA', //E = element, A = attribute, C = class, M = comment
            scope: {
                //@ reads the attribute value, = provides two-way binding, & works with functions
                docConcluirAtendimento: '='
            },
            templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/documentosConcluirAtendimento.html',
            //controller: controllerFunction, //Embed a custom controller in the directive
            link: function ($scope, element, attrs) {

                $scope.addDocumentoAnaliseRequerimento = function(documentos) {
                    angular.forEach(documentos, function(documento) {
                        arquivoServiceServicos.uploadArquivo(documento).then(function(resultado) {
                            documento.id = resultado.data.resposta[0];
                            $scope.docConcluirAtendimento.push(documento);
                        });
                    });
                };

                $scope.abrirDocumentoAnaliseRequerimento = function(index){
                    var image = new Image();
                    image.src = $scope.docConcluirAtendimento[index];
                    var w = window.open("");
                    w.document.write(image.outerHTML);
                };

                $scope.removerDocumentoAnaliseRequerimento = function(idArquivo, index) {
                    arquivoServiceServicos.apagar(idArquivo).then(function() {
                        $scope.docConcluirAtendimento.splice(index, 1);
                    });
                };

            }
        };
    };
  angular.module('su-servicos')
    .directive('documentosConcluirAtendimento',['comumServiceServico', 'arquivoServiceServicos', directive]);

})();

/**
 * Created by ingrid on 10-10-2016.
 */
(function () {

    'use strict';

    directive.$inject = ["comumServiceServico"];
    angular.module('su-servicos')
        .directive('breadcrumb', directive);

    function directive(comumServiceServico) {
        return {
            restrict: 'E', //E = element, A = attribute, C = class, M = comment
            scope: {
            },
            templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/breadcumb.html',
            //controller: controllerFunction, //Embed a custom controller in the directive
            link: function ($scope, element, attrs) {
                comumServiceServico.buscarNomeServico($scope.idServico).then(function (response) {
                    debugger
                    $scope.labelBreadCrumb = response.data.resposta[0];
                });
            }
        };
    }

})();

(function () {

    directive.$inject = ["$mdDialog", "arquivoServiceServicos"];
  angular.module('su-servicos')
      .directive('arquivosConcluirAtendimento', directive);

        function directive($mdDialog, arquivoServiceServicos) {

      return {
        restrict: 'EA', //E = element, A = attribute, C = class, M = comment
        scope: {
          //@ reads the attribute value, = provides two-way binding, & works with functions
          arquivos: '=',
          bloquearFormulario: '='
        },
        templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/arquivosConcluirAtendimento.html',
        //controller: controllerFunction, //Embed a custom controller in the directive
        link: function ($scope, element, attrs) {

          var vm = $scope;

          vm.puxouArquivos = false;

          vm.arquivos = [];

          vm.uploadArquivo = function(arquivos) {
              extrairNomesArquivos(arquivos);
              angular.forEach(arquivos, function(arquivo) {
                arquivoServiceServicos.uploadRetornarArquivo(arquivo).then(function(response) {
                  vm.arquivos.push(response.data.resposta[0]);
                });
              });
          };

          var extrairNomesArquivos = function(arquivos){
              vm.nomeArquivos = '';
              var i = 0;
              angular.forEach(arquivos, function(arquivo) {
                if (i > 0) {
                  vm.nomeArquivos += ',';
                }
                vm.nomeArquivos += (arquivo.name ? arquivo.name : arquivo.nome);
                i++;
              });
          };

          vm.removerArquivo = function(event, index) {

            var dialog = $mdDialog;
            var fechar = function(){
              dialog.hide();
              dialog = undefined;
            };

            var confirm = dialog.confirm({focusOnOpen: false})
              .content("Deseja remover este arquivo?")
              .ariaLabel("Excluir Arquivo")
              .cancel("Não")
              .ok("Sim");

            dialog.show(confirm).then(function (){

              vm.arquivos.splice(index, 1);

              fechar();

            }, function(){
              fechar();
            });
          };

          vm.abrirOutraAba = function(index){
            var image = new Image();
            image.src = vm.arquivos[index];
            var w = window.open("");
            w.document.write(image.outerHTML);
          };


          vm.buscarArquivosEnviadosAoCidadao = function(){
            if (arquivoServiceServicos.getRequerimento().statusRequerimento == "DEFERIDO"){
              vm.promise = arquivoServiceServicos.buscarArquivos();
              vm.promise.then(function(response){
                vm.arquivos = response.data.resposta;
                //console.log(vm.arquivos);
              });
            }
          }

          vm.realizarDownloadArquivo = function(id){
            if (id != null){
                var download = arquivoServiceServicos.download(id);
              if (download != null){
                return download;
              }else{
                vm.abrirOutraAba();
              }
            }else{
              vm.abrirOutraAba();
            }
          }


          vm.buscarArquivosEnviadosAoCidadao();
        
        }
      };
    }


})();

(function () {
  'use-strict';

  var directive = function (comumServiceServico, $state, $mdDialog, arquivoServiceServicos,
                      analiseSimplificadaService, escopoCompartilhadoService, requerimentoService, $filter, $rootScope) {
    return {
      restrict: 'EA', //E = element, A = attribute, C = class, M = comment
      scope: {
        //@ reads the attribute value, = provides two-way binding, & works with functions
        requerimento: '=',
        showAnalise: '='
      },
      templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/analiseTecnicaSimplificada.html',
      controller: function () {
        var vm = this;
        vm.registroInicial = 1;
        vm.tamanhoLimite = 5;
      }, //qEmbed a custom controller in the directive
      controllerAs: "ctrl",
      link: function ($scope, el, attrs, ctrl) {

        var vm = $scope;

        vm.analiseRequerimento = {};
        // vm.registroInicial = 1;
        // vm.tamanhoLimite = 5;
        vm.totalItems = 0;

        vm.existTipoDoc = {
          OBRIGATORIO: false,
          COMPLEMENTAR: false
        };

        vm.concluirAtendimento = false;
        vm.docConcluirAtendimento = [];
        vm.autuarProcesso = true;

        vm.STATUS_CANCELADO = 'CANCELADO';
        vm.STATUS_PROCESSO_EXISTENTE = 'PROCESSO_EXISTENTE';
        vm.STATUS_EM_ANALISE_TECNICA = 'EM_ANALISE_TECNICA';
        vm.STATUS_CONCLUIDO_TRIAGEM = 'CONCLUIDO_NA_TRIAGEM';
        vm.STATUS_DEFERIDO = 'DEFERIDO';
        vm.STATUS_INDEFERIDO = 'INDEFERIDO';
        vm.STATUS_NOVO_PROCESSO = 'NOVO_PROCESSO';
        vm.STATUS_AGUARDANDO_REQUERENTE = 'AGUARDANDO_REQUERENTE';
        vm.STATUS_NOVO_PROCESSO = 'NOVO_PROCESSO';
        vm.STATUS_AGUARDANDO_RETORNO_SEI = 'AGUARDANDO_RETORNO_SEI';
        vm.EM_ANALISE_PREVIA = 'EM_ANALISE_PREVIA';
        vm.AGUARDANDO_ANALISE_PREVIA = 'AGUARDANDO_ANALISE_PREVIA';

        var TIPO_OBRIGATORIO = 'OBRIGATORIO';
        var TIPO_COMPLEMENTAR = 'COMPLEMENTAR';

        var statusRequerimentoAnterior = undefined;


        var init = function () {

          vm.buscarAnaliseRequerimento();
          vm.buscarHistorico();
          arquivoServiceServicos.setRequerimento(vm.requerimento);
        };

        vm.checkField = function(historico){
          return historico.justificativa && historico.justificativa.split('\n').length > 3;
        };


        var isBloquearFormulario = function(){
            return [vm.STATUS_CANCELADO, vm.STATUS_INDEFERIDO, vm.STATUS_DEFERIDO, vm.STATUS_CONCLUIDO_TRIAGEM]
                          .indexOf(vm.requerimento.statusRequerimento) != -1;
        };

        var isAnaliseTecnica = function(){
          return [$scope.STATUS_EM_ANALISE_TECNICA]
                          .indexOf(vm.requerimento.statusRequerimento) != -1;
        };

        $scope.isTriagem = function(){
          return [$scope.EM_ANALISE_PREVIA]
                          .indexOf(vm.requerimento.statusRequerimento) != -1;
        };

        vm.isStatusEmAnaliseTecnica = function () {
          return isAnaliseTecnica();
        };

        vm.isStatusEmAnalisePrevia = function () {
          return vm.requerimento.statusRequerimento === vm.EM_ANALISE_PREVIA;
        };

        vm.isCampoObrigatorio = function(){
            return vm.analiseRequerimento.statusRequerimento != vm.STATUS_CANCELADO;
        };

        vm.buscarAnaliseRequerimento = function () {

            analiseSimplificadaService.buscarAnaliseRequerimentoId(vm.requerimento.id)
              .then(function (resposta) {

                vm.analiseRequerimento = resposta.data.resposta[0];

                if (!vm.analiseRequerimento) {

                    vm.analiseRequerimento = {
                      statusAnterior: angular.copy(vm.requerimento.statusRequerimento)
                    };
                  vm.criarCheckLists();

                } else {

                    verificarExisteDocumento(vm.analiseRequerimento.analiseDocumentoChecklist);
                }

                // vm.criarCheckLists();

                vm.concluirAtendimento = isAnaliseTecnica();
                vm.bloquearFormulario = isBloquearFormulario();
                vm.analiseRequerimento.arquivos = [];
            });
        };

        vm.buscarHistorico = function () {
          analiseSimplificadaService.buscarHistoricoPorRequerimentoId((ctrl.registroInicial - 1),
            ctrl.tamanhoLimite, vm.requerimento.id).then(function (resp) {
            vm.historicos = resp.data.resposta[0].content;
            vm.totalItems = resp.data.resposta[0].totalElements;
          });
        };

       vm.criarCheckLists = function () {
          vm.analiseRequerimento.analiseChecklist = [];
          vm.analiseRequerimento.analiseDocumentoChecklist = [];
          vm.analiseRequerimento.analiseTecnicaChecklist = [];

          /**
           * @param listItens = lista com os itens que serão convertidos
           * @param itemName = nome do atributo que será adicionado no json
           * @param textName = nome do campo que tem o texto na lista de itens
           * @param listResult = lista onde serão adicionados os valores convertidos
           */
          var addItemCheckList = function (listItens, itemName, textName, listResult) {
            angular.forEach(listItens, function (item) {
              var obj = {
                resposta: null,
                justificativa: null,
                text: item[textName]
              };
              //Caso seja um documento é necessário salvar o tipo
              if (itemName == 'documento') {
                obj.tipo = item.tipo;
              }
              obj[itemName] = item;
              listResult.push(obj);
            });

          };

          if (vm.requerimento.servico && vm.requerimento.servico.triagens) {
            var triagens = [];
            angular.forEach(vm.requerimento.servico.triagens, function(triagem){
              if(triagem.status){
                triagens.push(triagem);
              }
            });
            addItemCheckList(triagens,
                              'analiseTriagem',
                              'item',
                              vm.analiseRequerimento.analiseChecklist);
          }

         if (vm.requerimento.servico && vm.requerimento.servico.documentos) {

            comumServiceServico.pesquisarDocumentosComArquivos(
                  vm.requerimento.servico.id,
                  vm.requerimento.id,
                  vm.requerimento.requerente.tipoSolicitante.id).then(function(retorno){

              addItemCheckList(retorno.data.resposta,
                              'documento',
                              'nome',
                              vm.analiseRequerimento.analiseDocumentoChecklist);

              vm.analiseRequerimento.requerimento = {
                id: vm.requerimento.id
              };

              verificarExisteDocumento(vm.analiseRequerimento.analiseDocumentoChecklist);
            });
          }

         if (vm.requerimento.servico && vm.requerimento.servico.analisesTecnicas) {
           var analisesTecnicas = [];
           angular.forEach(vm.requerimento.servico.analisesTecnicas, function(analise){
             if(analise.status){
               analisesTecnicas.push(analise);
             }
           });
           addItemCheckList(analisesTecnicas,
             'analiseTecnica',
             'item',
             vm.analiseRequerimento.analiseTecnicaChecklist);
         }

        };

        var verificarExisteDocumento = function (documentos) {
          angular.forEach(documentos, function (doc) {

            if (doc.documento.tipo == TIPO_OBRIGATORIO) {
                vm.existTipoDoc.OBRIGATORIO = true;
            }
            if (doc.documento.tipo == TIPO_COMPLEMENTAR) {
                vm.existTipoDoc.COMPLEMENTAR = true;
            }
          });
        };

        var verificarMensagensEnvioConcluirAtendimento = function () {
          if (vm.concluirAtendimento) {

            if (vm.analiseRequerimento.statusRequerimento == vm.STATUS_DEFERIDO
                || vm.analiseRequerimento.statusRequerimento == vm.STATUS_INDEFERIDO) {
                vm.msgSucesso = $filter('translate')('msg-concluir-atendimento');
              } else if (vm.analiseRequerimento.statusRequerimento == vm.STATUS_AGUARDANDO_REQUERENTE) {
                vm.msgSucesso = $filter('translate')('msg-retornar-pendencias');
              } else if (vm.analiseRequerimento.statusRequerimento == vm.STATUS_CANCELADO) {
                vm.msgSucesso = $filter('translate')('msg-retornar-cancelamento');
              }
          }
        };


        var verificarArquivosConcluirAtendimento = function () {
          var resposta = false;
          if (vm.concluirAtendimento) {
            if ((vm.analiseRequerimento.statusRequerimento == vm.STATUS_DEFERIDO
              || vm.analiseRequerimento.statusRequerimento == vm.STATUS_INDEFERIDO)
              && vm.analiseRequerimento.arquivos.length == 0) {

              comumServiceServico.mostrarMensagemError($filter('translate')('msg-adicionar-arquivo-concluir-atendimento'));

              resposta = true;
            }
          }
          return resposta;
        };

        vm.exibirNumeroSei = function(procedimentoSEI, statusRequerimento){
          $mdDialog.show({
            controller: ['$scope', function ($scope) {
              $scope.procedimentoSEI = procedimentoSEI;
              $scope.statusRequerimento = statusRequerimento;
              $scope.fechar = function () {
                $mdDialog.cancel();
                $state.go('servicos.SERVICOS_TRIAGEM');
              };

            }],
            templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/partials/modalExibirSEI.html',
            parent: angular.element(document.body),
            clickOutsideToClose: false
          });
        };

        vm.exibirMensagemSeiIndisponivel = function(mensagemErro, arquivosErro){
          $mdDialog.show({
            controller: ['$scope', function ($scope) {

              $scope.mensagemErro = mensagemErro;
              $scope.arquivosErro = arquivosErro;

              $scope.fechar = function () {
                $mdDialog.cancel();
                $state.go('servicos.SERVICOS_TRIAGEM');
              };

            }],
            templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/partials/modalExibirMensagemErroSei.html',
            parent: angular.element(document.body),
            clickOutsideToClose: false,
            locals: {
              mensagemErro: mensagemErro,
              arquivosErro: arquivosErro
            }
          });
        };

        var validarPreenchimentoConcluirAtendimento = function() {
            var resposta = false;
            if (vm.concluirAtendimento) {
              if (vm.analiseRequerimento.statusRequerimento !== vm.STATUS_DEFERIDO
                && vm.analiseRequerimento.statusRequerimento !== vm.STATUS_INDEFERIDO
                && vm.analiseRequerimento.statusRequerimento !== vm.STATUS_AGUARDANDO_REQUERENTE
                && vm.analiseRequerimento.statusRequerimento !== vm.STATUS_CANCELADO) {

                comumServiceServico.mostrarMensagemError($filter('translate')('mensagem-campos-obrigatorios'));
                resposta = true;
              }
            }
            return resposta;
        };


       vm.salvar = function () {
          vm.enviou = true;

          vm.analiseRequerimento.documentos = vm.docConcluirAtendimento;

          if (verificarArquivosConcluirAtendimento()
          || validarPreenchimentoConcluirAtendimento()) {
            vm.enviou = false;
            return;
          }

            verificarMensagensEnvioConcluirAtendimento();

            vm.analiseRequerimento.nomeTecnicoResponsavel = $rootScope.usuarioLogado.nome;
            vm.requerimento.requerente.emailConfirmacao = vm.requerimento.requerente.email;
            if (vm.requerimento.requerente.representanteLegal != null){
                vm.requerimento.requerente.representanteLegal.emailConfirmacao = vm.requerimento.requerente.representanteLegal.email
            }



          requerimentoService.salvarComStatus(vm.requerimento, vm.analiseRequerimento.statusRequerimento).then(function () {

            analiseSimplificadaService.salvar(vm.analiseRequerimento, vm.requerimento.id)
              .then(function (resp) {

                if(resp.data.resposta[0].statusRequerimento == vm.STATUS_AGUARDANDO_RETORNO_SEI){

                  $scope.exibirMensagemSeiIndisponivel(resp.data.resposta[0].mensagemErroSei, resp.data.resposta[0].documentosErro);

                }else{
                  $scope.exibirNumeroSei(
                      resp.data.resposta[0].procedimentoSEI, vm.analiseRequerimento.statusRequerimento);
                }

              }, function (erro) {
                vm.enviou = false;
                // comumServiceServico.mostrarMensagemError(erro.data.mensagem[0]);
                $scope.exibirMensagemSeiIndisponivel(erro.data.mensagem[0]);
              });

            });
        };

        vm.apresentarProcessoSEI = function () {

          if(!vm.analiseRequerimento.analiseChecklist
                && !vm.analiseRequerimento.analiseDocumentoChecklist){
                return false;
          }

          var lista = vm.analiseRequerimento.analiseChecklist;
          var listaDocumento = vm.analiseRequerimento.analiseDocumentoChecklist;

          for(var i=0; i<lista.length; i++){
              if (!lista[i].resposta && lista[i].analiseTriagem.obrigatorio) {
                  return false;
              }
          }

          for(var i=0; i<listaDocumento.length; i++){
              if (!listaDocumento[i].resposta) {
                  return false;
              }
          }

          return true;
        };

        vm.abrirModalJustificativa = function (item) {

          $mdDialog.show({
            controller: ['$scope', function ($scope) {
              $scope.justificativa = item.justificativa;

              $scope.confirmar = function () {
                $mdDialog.hide($scope.justificativa);
              };

              $scope.fechar = function () {
                $mdDialog.cancel();
              };

            }],
            templateUrl: 'scripts-servicos/app/directives/partials/justificativa.html',
            parent: angular.element(document.body),
            clickOutsideToClose: true
          }).then(function (justificativa) {
            item.justificativa = justificativa;
          });
        };

        vm.downloadDocumento = function(doc) {

          var files = [];
          var idDocumento = doc.documento.id;

          angular.forEach(vm.requerimento.arquivosRequerimento, function (item) {
            if (item.idDocumento == idDocumento) {
              files.push(item);
            }
          });

          if (files.length > 1) {
            vm.abrirModalDownloadArquivos( files);
          } else {
            var link = document.createElement("a");
            link.setAttribute("href", '/servicos/api/public/requerimento/download/arquivo-documento?idRequerimento=' + $scope.requerimento.id + "&idDocumento=" + idDocumento);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        };

        vm.abrirModalDownloadArquivos = function ( files) {
          $mdDialog.show({
            controller: ['$scope', 'arquivoServiceServicos', function ($scope, arquivoServiceServicos) {
              $scope.files = files;

              $scope.fechar = function () {
                $mdDialog.cancel();
              };

              $scope.baixar = function (idArquivo) {
                return arquivoServiceServicos.download(idArquivo);
              };
            }],
            templateUrl: 'scripts-servicos/app/directives/partials/modalDocumentosAnaliseSimp.html',
            parent: angular.element(document.body),
            clickOutsideToClose: true
          }).then(function () {

          });
        };

        vm.fechar = function () {
          vm.showAnalise = false;
        };

        vm.verificarResposta = function (documento) {

            if (!documento.resposta) {
                vm.abrirModalJustificativa(documento);
            }else{
                documento.justificativa = undefined;
            }

        };


        var verificarCheckListsPreenchidos = function(error){

          var nomeAnalise = $filter('translate')('label-analise-tecnica');
          var erro = $filter('translate')('msg-checklist-nao-preenchido');

          for(var i=0; error.required && i < error.required.length; i++){

              var field = error.required[i];

              if(field.$invalid){

                if (field.$name.indexOf(nomeAnalise) != -1) {
                    comumServiceServico.mostrarMensagemError(erro);
                    throw erro;
                }
              }
          }
        };


         var isTodasJustificativasPreenchidas = function () {

            var error = $filter('translate')('msg-validacao-justificativa');

            if(vm.analiseRequerimento.statusRequerimento != vm.STATUS_CANCELADO){

                var lista = vm.analiseRequerimento.analiseChecklist
                                    .concat(vm.analiseRequerimento.analiseDocumentoChecklist);

                for(var i=0; i<lista.length; i++){

                    var item = lista[i];

                    if (!item.resposta && !item.justificativa) {

                        comumServiceServico.mostrarMensagemError(error);
                        throw error;
                    }
                }
            }
        };


        vm.validarFormulario = function(form){

            verificarCheckListsPreenchidos(form.$error);

            if (vm.isStatusEmAnalisePrevia()) {
              isTodasJustificativasPreenchidas();
            }


            if(form.$invalid){
              var msg = $filter('translate')('mensagem-campos-obrigatorios');
              comumServiceServico.mostrarMensagemError(msg);
              throw msg;
            }

            return true;
        };


        vm.alterarStatus = function(){

          vm.analiseRequerimento.atuacaoProcesso = undefined;
          vm.analiseRequerimento.numeroProcesso = undefined;

        };

        vm.statusRequerimentoObrigatorio = function(){

          return vm.analiseRequerimento.statusRequerimento && (
            vm.analiseRequerimento.statusRequerimento === vm.STATUS_INDEFERIDO ||
            vm.analiseRequerimento.statusRequerimento === vm.STATUS_CANCELADO ||
            vm.analiseRequerimento.statusRequerimento === vm.STATUS_AGUARDANDO_REQUERENTE);

        };

        init();

      }
    };

  }

  angular.module('su-servicos')
    .directive('analiseSimplificadaServicos', ['comumServiceServico', '$state', '$mdDialog', 'arquivoServiceServicos',
      'analiseSimplificadaService', 'escopoCompartilhadoService', 'requerimentoService', '$filter', '$rootScope',directive]);
})();

(function () {
  'use-strict'
  directive.$inject = ["comumServiceServico", "FormularioFactory", "$mdDialog", "$filter", "servicoService"];
  angular.module('su-servicos')
    .directive('adicionarChecklist', directive);

  function directive (comumServiceServico, FormularioFactory, $mdDialog, $filter, servicoService) {
    return {
      restrict: 'EA', //E = element, A = attribute, C = class, M = comment
      scope: {
        listaChecklist: '=ngModel',
        checklists: '=',
        labelNome: '=',
        labelExcluirItemChecklist: '=',
        nomeChecklist: '='
      },
      templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/adicionarChecklist.html',
      //controller: controllerFunction, //qEmbed a custom controller in the directive
      link: function ($scope) {

        var vm = $scope;

        var init = function () {

          vm.queryChecklist = {
            order: 'item',
            limit: 10,
            limitsPage: [5, 10, 20],
            page: 1
          };

        };

        vm.modalCheckList = function (ev) {
          var modalChecklists = vm.listaChecklist ? angular.copy(vm.listaChecklist) : [];
          var nomeChecklist = vm.nomeChecklist;
          var labelExcluirItemChecklist = vm.labelExcluirItemChecklist;

          $mdDialog.show({
            controller: ['$scope', function ($scope) {

              var init = function(){

                $scope.modalChecklists = modalChecklists ? modalChecklists : [];
                $scope.nomeChecklist = nomeChecklist;
                $scope.labelExcluirItemChecklist = labelExcluirItemChecklist;

                $scope.queryChecklist = {
                  order: 'item',
                  limit: 5,
                  limitsPage: [5, 10, 20],
                  page: 1
                };
              };

              var validarDuplicidade = function () {
                for (var i = 0; i < $scope.modalChecklists.length; i++) {

                  var textoLista = comumServiceServico
                    .htmlToPlaintext($scope.modalChecklists[i].item);

                  var textoNovo = comumServiceServico
                    .htmlToPlaintext($scope.novoItemChecklist.item);

                  if (textoLista == textoNovo) {
                    return true;
                  }
                }
                return false;
              };

              $scope.validar = function(){

                if(!FormularioFactory.validarFormularioComMensagem(
                    $scope.formularioAnalise, $filter('translate')('error-item-analise-tecnica'))){
                  return false;
                }

                if(!servicoService.validarMinLength($scope.novoItemChecklist.item)){
                  return false;
                }

                if(validarDuplicidade()){
                  comumServiceServico.mostrarMensagemError($filter('translate')('erro-campo-duplicado', {
                    campo: $scope.nomeChecklist
                  }));
                  return false;
                }

                return true;
              };

              $scope.addItemChecklist = function () {

                if(!$scope.formularioAnalise.invalid){

                  $scope.novoItemChecklist.status = true;

                  $scope.novoItemChecklist.obrigatorio =
                    ($scope.novoItemChecklist.obrigatorio == true);

                  $scope.modalChecklists.push(
                    angular.copy($scope.novoItemChecklist));

                  $scope.novoItemChecklist = undefined;
                }
              };

              $scope.removerChecklist = function ($event, index) {
                $scope.modalChecklists.splice(index, 1);
              };
              $scope.closeDialog = function () {
                $mdDialog.cancel();
              };
              $scope.confirmarChecklist = function () {

                servicoService.setarOrdem($scope.modalChecklists);

                vm.listaChecklist = $scope.modalChecklists;
                $mdDialog.hide();
              };

              $scope.ordemChecklist = vm.ordenaLista;

              init();

            }],
            templateUrl: 'scripts-servicos/app/directives/utilitarios/templates/partials/modalAddChecklist.html',
            parent: angular.element(document.body),
            targetEvent: ev,
            clickOutsideToClose: false,
            locals: {
              modalChecklists: angular.copy($scope.modalChecklists)
            }
          }).then(function (modalChecklists) {

          });

        };

        vm.removerChecklist = function (ev, item) {
          var confirm = $mdDialog.confirm({focusOnOpen: false})
            .title(vm.labelNome)
            .textContent('Deseja confirmar exclusão?')
            .targetEvent(ev)
            .ok('Sim')
            .cancel('Não');

          $mdDialog.show(confirm).then(function () {
            var index = vm.listaChecklist.indexOf(item);

            if (vm.listaChecklist[index].id) {
              var indexChecklist = vm.checklists.indexOf(item);
              vm.checklists[indexChecklist].status = false;
            }

            vm.listaChecklist.splice(index, 1);

          }, function () {
            $mdDialog.cancel();
          });
        };

        vm.ordenaLista = function (atual, destino, lista) {
          var item = lista[atual];
          lista[atual] = lista[destino];
          lista[destino] = item;
        };

        init();

      }
    };

  }
})();

(function () {

    var directive = function (requerimentoService, $state) {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '=',
                showAnalise: '='
            },
            templateUrl: 'scripts-servicos/app/directives/footer/templates/acoesRequerimentoServico.html',
            link: function ($scope) {

                var vm = $scope;

                vm.EM_ANALISE_PREVIA = 'EM_ANALISE_PREVIA';
                vm.AGUARDANDO_ANALISE_PREVIA = 'AGUARDANDO_ANALISE_PREVIA';

                vm.iniciarAnalise = function() {

                    vm.requerimento.statusRequerimento = vm.EM_ANALISE_PREVIA;

                    requerimentoService.alterarStatus({
                            id: vm.requerimento.id,
                            statusRequerimento: vm.requerimento.statusRequerimento
                        }).then(function (response) {

                    });
                };

                vm.fechar = function () {
                    $state.go('servicos.SERVICOS_TRIAGEM');
                };



            }
        };
    };

    angular
        .module('su-servicos')
        .directive('acoesRequerimentoServico',[ 'requerimentoService', '$state', directive]);

})();

(function () {

    var directive = function () {
        return {
            restrict: 'EA',
            scope: {
                isAutenticado: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/mensagemAutenticacao.html'
        };
    };

    angular
        .module('su-servicos')
        .directive('mensagemAutenticacao', [directive]);

})();

(function () {

    'use strict';

    angular
        .module('su-servicos')
        .directive('enderecoDadoComplementar', directive);

    function directive () {
        return {
            restrict: 'EA',
            scope: {
                dado: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/enderecoDadoComplementar.html',
          link: function ($scope) {
            console.log($scope.dado);
          }
        };
    };

})();

(function () {

  angular.module('su-servicos').directive('detalharTransmitente', directive);

  function directive() {
    return {
      restrict: 'EA',
      scope: {
        transmitente: '='
      },
      templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharTransmitente.html',
      link: function ($scope) {

        var FIXO = 'FIXO';
        var COMERCIAL = 'COMERCIAL';
        var CELULAR = 'CELULAR';

        var tratarTelefone = function () {
          if ($scope.transmitente) {
            angular.forEach($scope.transmitente.telefones, function (elem) {
              if (elem.tipoTelefone == FIXO) {
                $scope.transmitente.telefoneFixo = elem.numero ? elem.numero : '-';
              } else if (elem.tipoTelefone == CELULAR) {
                $scope.transmitente.telefoneCelular = elem.numero ? elem.numero : '-';
              } else if (elem.tipoTelefone == COMERCIAL) {
                $scope.transmitente.telefoneComercial = elem.numero ? elem.numero : '-';
              }
            });
          }
        };

        $scope.$watch('transmitente', function (newValue) {
          if (newValue) {
            tratarTelefone(newValue.telefones);
          }
        });
      }
    };
  }

}());

(function () {

  angular.module('su-servicos').directive('detalharTransferenciaImovel', adquirenteTitular);

  function adquirenteTitular() {
    return {
      restrict: 'EA',
      scope: {
        requerimento: '='
      },
      templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharTransferenciaImovel.html',
      link: function () {

      }
    }
  }

})();

(function () {

    var directive = function () {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharTitulo.html'
        };
    };

    angular
        .module('su-servicos')
        .directive('detalharTitulo', [directive]);

})();

(function () {

    var directive = function () {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '=',
                tipo:'='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharRequerente.html',
            link: function ($scope, element, attrs) {

                var FIXO = 'FIXO';
                var COMERCIAL = 'COMERCIAL';
                var CELULAR = 'CELULAR';
                var CONSULTA_DOMINIALIDADE = 'CONSULTA_DOMINIALIDADE';

                var tratarTelefone = function (tipo) {

                    if ($scope.requerimento) {
                        angular.forEach($scope.requerimento.requerente.telefones, function (elem) {
                           if (elem.tipoTelefone == FIXO) {
                               $scope.requerimento.requerente.telefoneFixo = elem.numero ? elem.numero : '-';
                           } else if (elem.tipoTelefone == CELULAR) {
                               $scope.requerimento.requerente.telefoneCelular = elem.numero ? elem.numero : '-';
                           } else if (elem.tipoTelefone == COMERCIAL) {
                               $scope.requerimento.requerente.telefoneComercial = elem.numero ? elem.numero : '-';
                           }
                        });
                    }

                };

                $scope.$watch('requerimento.requerente', function (newValue) {
                    if (newValue) {
                        tratarTelefone(newValue.telefones);
                    }
                });

                $scope.verificarTipo = function () {
                    return $scope.tipo && $scope.tipo == CONSULTA_DOMINIALIDADE;
                };

            }
        };
    };

    angular
        .module('su-servicos')
        .directive('detalharRequerente',[directive]);

})();

(function () {

    var directive = function () {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '=',
                tipo:'='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharRepresentanteLegal.html',
            link: function ($scope, element, attrs) {

                const FIXO = 'FIXO';
                const COMERCIAL = 'COMERCIAL';
                const CELULAR = 'CELULAR';
                const CONSULTA_DOMINIALIDADE = 'CONSULTA_DOMINIALIDADE';

                var tratarTelefone = function (tipo) {

                    if ($scope.requerimento && $scope.requerimento.requerente && $scope.requerimento.requerente.representanteLegal) {
                        angular.forEach($scope.requerimento.requerente.representanteLegal.telefones, function (elem) {
                            if (elem.tipoTelefone == FIXO) {
                                $scope.requerimento.requerente.representanteLegal.telefoneFixo = elem.numero ? elem.numero : '-';
                            } else if (elem.tipoTelefone == CELULAR) {
                                $scope.requerimento.requerente.representanteLegal.telefoneCelular = elem.numero ? elem.numero : '-';
                            } else if (elem.tipoTelefone == COMERCIAL) {
                                $scope.requerimento.requerente.representanteLegal.telefoneComercial = elem.numero ? elem.numero : '-';
                            }
                        });
                    }

                };

                $scope.$watch('requerimento.requerente', function (newValue) {
                    if (newValue) {
                        tratarTelefone(newValue.telefones);
                    }
                });

                $scope.verificarTipo = function () {
                    return $scope.tipo && $scope.tipo == CONSULTA_DOMINIALIDADE;
                };

            }
        };
    };

    angular
        .module('su-servicos')
        .directive('detalharRepresentanteLegal',[directive]);

})();

(function () {

  directive.$inject = ["arquivoServiceServicos", "$mdDialog", "$filter"];
  angular.module('su-servicos').directive('detalharPrioridadeAtendimento', directive);

  function directive (arquivoServiceServicos, $mdDialog, $filter) {
    return {
      restrict: 'EA', //E = element, A = attribute, C = class, M = comment
      scope: {
        //@ reads the attribute value, = provides two-way binding, & works with functions
        requerimento: '=',
        bloquear:'='
      },
      templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharPrioridadeAtendimento.html',
      link: function ($scope) {


        function init() {

          if(angular.isUndefined($scope.requerimento.prioridadeAtendimento)){
            $scope.requerimento.prioridadeAtendimento = {
              id : null,
              prioridadeBetween : false,
              prioridadeAcima : false,
              prioridadeDoenca : false,
              arquivos : []

            };
          }
        }

        $scope.download = function (id) {
          return arquivoServiceServicos.download(id);
        };


        init();
      }
    };
  };

})();


(function () {

    var directive = function ($filter) {
        return {
            restrict: 'EA', //E = element, A = attribute, C = class, M = comment
            scope: {
                requerimento: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharInformacoesSocioEconomicas.html',
            link: function ($scope, element, attrs) {

            }
        };
    };

    angular.module('su-servicos')
        .directive('detalharInformacoesSocioEconomicas',['$filter', directive]);

})();

(function () {

    var directive = function (comumServiceServico) {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '=',
                previewFotos: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharImovelUniao.html',
            link: function ($scope, element, attrs) {

                var init = function() {

                };

                $scope.visualizarImagem = function(foto) {
                    comumServiceServico.gerarPreviewImagens(foto, function (src) {
                        return src;
                    });
                };

                init();

            }
        };
    };

    angular
        .module('su-servicos')
        .directive('detalharImovelUniao' ,['comumServiceServico', directive]);

})();

(function () {
  var directive = function () {
      return {
        restrict: 'EA',
        scope: {
          requerimento: '=',
          tipo: '='
        },
        templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharImovel.html',
        link: function ($scope, element, attrs) {

          var FIXO = 'FIXO';
          var COMERCIAL = 'COMERCIAL';
          var CELULAR = 'CELULAR';
          var CONSULTA_DOMINIALIDADE = 'CONSULTA_DOMINIALIDADE';
          var NOVA_CONSULTA_DOMINIALIDADE= 'NOVA_CONSULTA_DOMINIALIDADE'
          var REQUERIMENTO_REVISAO_VALOR_AREA = 'REQUERIMENTO_REVISAO_VALOR_AREA';
          var TRANSFERENCIA_TITULAR = 'TRANSFERENCIA_TITULAR';
          var AUTORIZACAO_OBRAS =  'AUTORIZACAO_OBRAS';
          var ISENCAO_PAGAMENTO_TAXA_OCUPACAO_FORO = 'ISENCAO_PAGAMENTO_TAXA_OCUPACAO_FORO';

          $scope.previewFoto = [];

          $scope.verificarTipo = function () {
            if(!$scope.requerimento){
              return;
            }

            var containsTipo = false;

            if($scope.requerimento.tipoRequerimento != undefined){
              containsTipo = [CONSULTA_DOMINIALIDADE].indexOf($scope.requerimento.tipoRequerimento)  != -1;
            } else if ($scope.requerimento.tipoRequerimento != undefined && $scope.requerimento.tipoRequerimento != CONSULTA_DOMINIALIDADE) {
              debugger
              containsTipo = [NOVA_CONSULTA_DOMINIALIDADE].indexOf($scope.requerimento.tipoRequerimento)  != -1;
            }
            return containsTipo;
          };

          $scope.isAutorizacaoObra = function(){
            if(!$scope.requerimento){
              return;
            }

            return($scope.requerimento.tipoRequerimento != undefined)
                && $scope.requerimento.tipoRequerimento == AUTORIZACAO_OBRAS;
          }

          $scope.isIsencao = function () {

            if(!$scope.requerimento){
              return;
            }
            return($scope.requerimento.tipoRequerimento != undefined)
                  && $scope.requerimento.tipoRequerimento == ISENCAO_PAGAMENTO_TAXA_OCUPACAO_FORO;
          }

          $scope.isConsultaDominialidade = function(){
            if(!$scope.requerimento){
              return;
            }

            return($scope.requerimento.tipoRequerimento != undefined)
              && $scope.requerimento.tipoRequerimento == CONSULTA_DOMINIALIDADE;

          }

          $scope.isNovaConsultaDominialidade = function () {
            if(!$scope.requerimento){
              return;
            }
            return($scope.requerimento.tipoRequerimento != undefined)
              && $scope.requerimento.tipoRequerimento == NOVA_CONSULTA_DOMINIALIDADE;
          }

          $scope.isTransferenciaTitular = function(){
            if(!$scope.requerimento){
              return;
            }

            return ($scope.requerimento.tipoRequerimento != undefined)
              && $scope.requerimento.tipoRequerimento == TRANSFERENCIA_TITULAR;
          };

          $scope.isValorArea = function(){
            if(!$scope.requerimento){
              return;
            }

            return($scope.requerimento.tipoRequerimento != undefined)
              && $scope.requerimento.tipoRequerimento == REQUERIMENTO_REVISAO_VALOR_AREA;

          };

          $scope.getUsoImovelFormatado = function(){

            var str = [];

            if($scope.requerimento
              && $scope.requerimento.imovel
              && $scope.requerimento.imovel.destinacoes){

              angular.forEach($scope.requerimento.imovel.destinacoes, function(item){

                str.push(item);
              });

            }

            return str.join('/');
          };


          $scope.$watch('requerimento.imovel', function (newValue) {
            if (newValue) {
              if($scope.requerimento.imovel && $scope.requerimento.imovel.fotosImove){
                angular.forEach($scope.requerimento.imovel.fotosImovel, function (foto) {

                  if(foto && foto.base64){
                    $scope.previewFoto.push(foto.base64);
                  }
                });
              }
            }
          });
        }
      };
    }
    ;

  angular
    .module('su-servicos')
    .directive('detalharImovel', directive);

})();

(function () {

    var directive = function () {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharFormaAquisicao.html',
            link: function (scope, element, attrs) {

            }
        };
    };

    angular
        .module('su-servicos')
        .directive('detalharFormaAquisicao',[directive]);

})();

(function () {

    var directive = function (comumServiceServico) {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharEvento.html',
            link: function ($scope) {

                $scope.$watch('requerimento.requerente.evento', function (newValue) {

                    if(newValue){
                        $scope.dataInicialUtilizacao = $scope.requerimento.requerente.evento.dataInicialUtilizacao;
                        $scope.dataFinalUtilizacao = $scope.requerimento.requerente.evento.dataFinalUtilizacao;

                        $scope.dataInicialRealizacao = $scope.requerimento.requerente.evento.dataInicialRealizacao;
                        $scope.dataFinalRealizacao = $scope.requerimento.requerente.evento.dataFinalRealizacao;

                        $scope.diasUtilizacao = comumServiceServico.calcularDiasEntreDatas(new Date($scope.dataInicialUtilizacao),
                          new Date($scope.dataFinalUtilizacao));

                        $scope.diasRealizacao = comumServiceServico.calcularDiasEntreDatas(new Date($scope.dataInicialRealizacao),
                          new Date($scope.dataFinalRealizacao));
                    }
                });

            }
        };
    };

    angular
        .module('su-servicos')
        .directive('detalharEvento',['comumServiceServico', directive]);

})();

(function () {

    var directive = function (comumServiceServico, $injector) {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharEnderecoCorrespondencia.html',
            link: function (scope, element, attrs) {

            }
        };
    };

    angular
        .module('su-servicos')
        .directive('detalharEnderecoCorrespondencia',[directive]);

})();

(function () {

    var directive = function (comumServiceServico, arquivoServiceServicos, $filter, $http, $mdDialog) {
        return {
            restrict: 'EA', //E = element, A = attribute, C = class, M = comment
            scope: {
                //@ reads the attribute value, = provides two-way binding, & works with functions
                idServico: '=',
                idTipoSolicitante: '=',
                tipoSolicitante: '=',
                idRequerimento: '=idRequerimento'
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharDocumento.html',
            //controller: controllerFunction, //Embed a custom controller in the directive
            link: function ($scope) {

                $scope.documento = {
                    documentosObrigatorios: [],
                    documentosComplementares: [],
                    outrosDocumentos: []
                };

                var POSSUO = 'POSSUO';
                var NAO_POSSUO = 'NAO_POSSUO';
                var POSSO_CONSEGUIR = 'POSSO_CONSEGUIR';

                $scope.$watch('idTipoSolicitante', function (newValue) {
                    if (newValue && $scope.idServico) {
                        comumServiceServico.pesquisarDocumentosUtilizados(
                                $scope.idServico, $scope.idRequerimento, newValue)
                                .then(function (resultado) {

                            $scope.documento.documentosObrigatorios = [];
                            $scope.documento.documentosComplementares = [];
                            filtrarTiposDocumentos(resultado.data.resposta);
                            getDocumentosSalvos();
                        });
                    }
                });

                var filtrarTiposDocumentos = function (documentos) {
                  angular.forEach(documentos, function (documento) {
                      if (documento.tipo == 'OBRIGATORIO') {
                          $scope.documento.documentosObrigatorios.push(documento);
                      } else if (documento.tipo == 'COMPLEMENTAR') {
                          $scope.documento.documentosComplementares.push(documento);
                      }
                  });
                };

                $scope.removerArquivo = function (event, documento, arquivo) {
                    comumServiceServico.modalConfirmacao(event,
                        $filter('translate')('label-alerta'),
                        $filter('translate')('mensagem-confirmar-exclusao'), function () {
                            var index = documento.files.findIndex(function (elem) {
                                return arquivo.name == elem.name
                            });
                            var arquivoTmp = documento.files[index];
                            arquivoServiceServicos.apagar(arquivoTmp.id).then(function (response) {
                                documento.files.splice(index, 1);
                            }, function (erro) {
                                comumServiceServico.exibirErro(erro.data.mensagem);
                            });

                        });
                };

                $scope.open = function(documento){
                    comumServiceServico.visualizarArquivo(documento);
                };

                var getDocumentosSalvos = function () {
                    if ($scope.idRequerimento) {
                        $http.get('servicos/api/public/arquivo-requerimento/requerimento/' + $scope.idRequerimento).then(function (response) {
                            var arquivos = response.data.resposta;
                            getOutrosDocumentosSalvos(arquivos);
                            carregarDocumentos(arquivos, $scope.documento.documentosObrigatorios);
                            carregarDocumentos(arquivos, $scope.documento.documentosComplementares);
                        });
                    }
                };

                var getOutrosDocumentosSalvos = function(arquivos) {
                    if ($scope.idRequerimento) {
                        $http.get('servicos/api/public/arquivo-requerimento/outrosDocumentos/requerimento/' + $scope.idRequerimento).then(function (response) {
                            var documentos = response.data.resposta;
                            if (documentos && documentos.length > 0) {
                                $scope.documento.outrosDocumentos = _.uniqBy(documentos, 'id');
                                carregarDocumentos(arquivos, $scope.documento.outrosDocumentos);
                            }
                        });
                    }
                };

                var carregarDocumentos = function(arquivos, documentos) {
                    angular.forEach(documentos, function (documento) {
                        if(arquivos) {
                            documento.files = [];
                            angular.forEach(arquivos, function (arquivo) {
                                if (arquivo.idDocumento == documento.id) {
                                    documento.situacaoDocumentoComplementar = arquivo.situacaoDocumentoComplementar;
                                    if (arquivo.situacaoDocumentoComplementar == NAO_POSSUO
                                          || arquivo.situacaoDocumentoComplementar == POSSO_CONSEGUIR) {
                                      documento.idRequerimentoArquivo = arquivo.id;
                                    } else {
                                      documento.files.push({id: arquivo.id, idArquivo: arquivo.idArquivo, name: arquivo.name, naoRemover : true});
                                  }
                                }
                            });
                        }
                    });
                };

				$scope.formatarTextoSituacao = function (situacao) {

                  if (situacao == NAO_POSSUO) {
                    return $filter('translate')('nao-possuo');
                  } else if (situacao == POSSO_CONSEGUIR) {
                    return $filter('translate')('nao-possuo-consegui');
                  } if (situacao == POSSUO) {
                    return $filter('translate')('possuo');
                  }
                };

                $scope.download = function (id) {
                    return arquivoServiceServicos.download(id);
                };

            }
        };
    };

    angular.module('su-servicos')
        .directive('detalharDocumento',['comumServiceServico', 'arquivoServiceServicos', '$filter', '$http', '$mdDialog', directive]);

})();

(function () {
  angular
      .module('su-servicos')
      .directive('detalharDadosRegimeContrato',[directive]);

    function directive () {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '=',
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharDadosRegimeContrato.html',
            link: function ($scope, element, attrs) {


            }
        };
    };

})();

(function () {

  var directive = function ($filter) {
    return {
      restrict: 'EA', //E = element, A = attribute, C = class, M = comment
      scope: {
        requerimento: '='
      },
      templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharCancelamentoUtilizacao.html',
      link: function ($scope, element, attrs) {

        var init = function() {
        };

        $scope.$watch('requerimento.cancelamentoUtilizacao.motivoCancelamento', function (newValue) {
          $scope.labelTiposMotivoCancelamento = '';
          var i = 0;
          angular.forEach(newValue, function(tipoCancelamento) {
            if (i > 0) {
              $scope.labelTiposMotivoCancelamento += ',';
            }
            $scope.labelTiposMotivoCancelamento += tipoCancelamento.descricao;
            i++;
          });
        });

        init();

      }
    };
  };

  angular.module('su-servicos')
    .directive('detalharCancelamentoUtilizacao',['$filter', directive]);

})();

(function () {

    var directive = function (comumServiceServico) {
        return {
            restrict: 'EA', //E = element, A = attribute, C = class, M = comment
            scope: {
                //@ reads the attribute value, = provides two-way binding, & works with functions
                requerimento: "="
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharCancelamentoCobranca.html',
            //controller: controllerFunction, //Embed a custom controller in the directive
            link: function ($scope, element, attrs) {

                var init = function(){

                };

              $scope.$watch('requerimento.cancelamentoCobranca.anosDebito', function (newValue) {
                $scope.labelAnosDebitos = '';
                var i = 0;
                angular.forEach(newValue, function(debito) {
                  if (i > 0) {
                    $scope.labelAnosDebitos += ', ';
                  }
                  $scope.labelAnosDebitos += debito.anoDebito;
                  i++;
                });
              });

                init();
            }
        };
    };

    angular.module('su-servicos')
        .directive('detalharCancelamentoCobranca',['comumServiceServico', directive]);

})();

/**
 * Created by SPU on 30/08/2016.
 */
(function () {

  var directive = function () {
    return {
      restrict: 'EA',
      scope: {
        requerimento: '='
      },
      templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharAutorizacao.html',
      link: function (scope, element, attrs) {

      }
    };
  };

  angular
    .module('su-servicos')
    .directive('detalharAutorizacao',[directive]);

})();

(function () {

    var directive = function () {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharAreaEvento.html'
        };
    };

    angular
        .module('su-servicos')
        .directive('detalharAreaEvento',[directive]);

})();

(function () {

    angular
        .module('su-servicos')
        .directive('detalharAdquirenteTitular',[adquirenteTitular]);

    function adquirenteTitular() {
        return {
            restrict: 'EA',
            scope: {
                requerimento: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/detalharAdquirenteTitular.html',
            link: function (scope, element, attrs) {

            }
        };
    }



})();

(function () {

    'use strict';

    angular
        .module('su-servicos')
        .directive('dadosComplementar', directive);

    function directive () {
        return {
            restrict: 'EA',
            scope: {
                camposDadosComplementares: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/dadosComplementar.html',
          link: function ($scope) {
          }
        };
    };

})();

(function () {

    'use strict';

    directive.$inject = ["arquivoServiceServicos"];
    angular
        .module('su-servicos')
        .directive('arquivosAjuda', directive);

    function directive (arquivoServiceServicos) {
        return {
            restrict: 'EA',
            scope: {
                listaArquivo: '='
            },
            templateUrl: 'scripts-servicos/app/directives/detalhar-requerimento/templates/arquivosAjuda.html',
            link: function ($scope) {

                var vm = $scope;
                vm.downloadArquivo = arquivoServiceServicos.download;

                var init = function(){
                    vm.arquivosAjuda = angular.copy(vm.listaArquivo);
                };

                init();

            }
        };
    };

})();

(function () {

  'use strict';

  angular
    .module('su-servicos')
    .factory('cloneUtils', function(){

      var clone = function(target, object){
        if(!object){
          return;
        } else if(!target){
          target = {};
        }
        if(isArray(target) && isArray(object)){
          cloneList(target, object);
          return;
        }
        clear(target);
        for(var key in object){
          if(object.hasOwnProperty(key)) {
            target[key] = object[key];
          }
        }
      };

      var clear = function(object){
        if(!object){
          object = {};
        }
        for(var key in object){
          if(object.hasOwnProperty(key)) {
            object[key] = undefined;
          }
        }
      };

      var cloneList = function(targetList, list){
        if(!targetList){
          targetList = [];
        }
        if(targetList.length > list.length) {
          var limit = targetList.length - (targetList.length - list.length);
          targetList.splice(limit, limit);
        }
        for(var i = 0; i < list.length; i++) {
          if(!targetList[i]){
            targetList[i] = {};
          }
          clone(targetList[i], list[i]);
        }
      };

      var isArray = function(object){
        return object.constructor === Array;
      };

      return {
        clone: clone,
        cloneList: cloneList
      };

    });

})();

(function () {

    'use strict';

    angular
        .module('su-servicos')
        .factory('requerimentoService', ['$http', '$state', function ($http, $state) {

            var resource = 'servicos/api/public/requerimento';

            var resourceProtegido = 'servicos/api/requerimento';

            var findById = function(id){
                return $http.get(resource + '/' + id);
            };

            var recuperarRequerimento = function (id) {
                return $http.get(resource + '/' + id);
            };

            var alterarStatus = function (requerimento) {
                return $http.put(resource + '/alterar-status', requerimento);
            };

            var salvar = function(requerimento){
                return $http.post(resource, requerimento);
            };

            var salvarComStatus = function(requerimento, statusRequerimento){
                return $http.post(resource + '/status/' + statusRequerimento, requerimento);
            };

            var recuperarRequerimentoPorCodigoIdentificacao = function (codigoIdentificacao, callback) {
                $http.get(resourceProtegido + '/codigoIdentificacao/' + codigoIdentificacao)
                        .then(function(res){
                            callback(res);
                }, function(){
                            $state.transitionTo('servicos.SERVICOS_TRIAGEM');
                });
            };

            var verificarReqPossuiInfoSocioEconomicas = function (requerimento) {
              if (requerimento && requerimento.requerente) {
                if (requerimento.requerente.cpfCnpj) {
                  return requerimento.requerente.cpfCnpj.length == 11;
                }
              }
              return false;
            };
            var isDetalhar = function(){
                return $state.current.name.indexOf('/detalhar') != -1;
            };

            return {
                recuperarRequerimento: recuperarRequerimento,
                findById: findById,
                alterarStatus: alterarStatus,
                recuperarRequerimentoPorCodigoIdentificacao: recuperarRequerimentoPorCodigoIdentificacao,
                verificarReqPossuiInfoSocioEconomicas: verificarReqPossuiInfoSocioEconomicas,
                isDetalhar:isDetalhar,
                salvar: salvar,
                salvarComStatus: salvarComStatus
            };

        }]);

})();

(function(){
'use strict';
  service.$inject = ["$http", "$translate", "ComumFactory"];
angular
    .module('su-servicos')
    .factory('comumServiceServico', service);

    function service ($http, $translate, ComumFactory) {

        var buscarTiposAssuntos = function () {
            return $http.get('/servicos/api/public/tipo-assunto')
        };

        var listarFormularioWeb = function () {
            return $http.get('/servicos/api/formulario-web');
        };

        var tiposDocumentos = function() {
            return $http.get('/servicos/api/public/comum/documento/tipos');
        };

        var listarFormatosArquivo = function () {
            return $http.get('/servicos/api/public/comum/formato-arquivo');
        };

        var listarProfissoes = function () {
            return $http.get('/servicos/api/public/comum/profissoes');
        };

        var pesquisarDocumentos = function (servicoId, solicitanteId) {
            return $http.get('/servicos/api/public/servico/pesquisar/documentos/' + servicoId + '/' + solicitanteId);
        };

        var pesquisarDocumentosUtilizados= function (servicoId, requerimentoId, solicitanteId) {
            return $http.get('/servicos/api/public/servico/pesquisar/documentos-utilizados/' + servicoId + '/' + requerimentoId + '/' +solicitanteId);
        };

        var pesquisarDocumentosComArquivos = function (servicoId, requerimentoId, solicitanteId) {
            return $http.get('/servicos/api/servico/pesquisar/documentos-com-arquivos/' + servicoId + '/' + requerimentoId + '/' +solicitanteId);
        };


        var mostrarCamposInvalidos = function (form) {
            $translate(['msg-campos-obrigatorios']).then(function(msg){
                var msgCamposObrigatorios = msg['msg-campos-obrigatorios'];
                var error = form.$error;
                var mensagens = [""];
                angular.forEach(error.required, function (field) {
                    if (field.$invalid) {
                        if (!verificarExisteCampo(mensagens, field.$name))
                            mensagens.push(field.$name);
                    }
                });
                exibirMesagemErroVariosCampos(msgCamposObrigatorios, mensagens);
            });
        };

        var verificarExisteCampo = function (mensagens, campo) {
            var existe = false;
            angular.forEach(mensagens, function (item) {
                if (item == campo)
                    existe = true;
            });
            return existe;
        };

        var listarTiposLogradouro = function () {
            return $http.get('/servicos/api/public/comum/logradouro/descricao');
        };

        var listarTodasUfs = function () {
            return $http.get('/servicos/api/public/comum/localidade/ufs/siglas');
        };

        var listarTodosNomesMunicipios = function (uf) {
            return $http.get('/servicos/api/public/comum/localidade/municipios/nomes/' + uf);
        };

        var calcularDiasEntreDatas = function (dataInicial, dataFinal) {
            var oneDay = 24*60*60*1000; // hours*minutes*seconds*milliseconds
            return Math.round(Math.abs((dataFinal.getTime() - dataInicial.getTime())/(oneDay)))+1;
        };

        var buscarIdSistema = function () {
          return $http.get('servicos/api/public/comum/buscar-id-sistema');
        };

        var htmlToPlaintext = function(text) {
            var raw = text ? String(text).replace(/<[^>]+>/gm, '') : '';
            return raw.replace(/&nbsp;/g, '')
                      .replace(/\n/g, '');
        };

        var isNumeroInteiro = function(numero){
            return Math.floor(numero) == numero;
        };

        var resertarFormulario = function(scope, nomeFormulario){
             scope[nomeFormulario].$setPristine();
             scope[nomeFormulario].$setUntouched();
        };

        var arrendondarDuasCasasDecimais = function(valor){
            return Math.round(valor * 100) / 100;
        };

        var formatDate = function(date){
            var format = undefined;
            if(date){
                format = moment(date).format('DD/MM/YYYY');
            }
            return format;
        };

        var pesquisaDadosComplemntar = function (servicoId) {
            return $http.get('/servicos/api/public/dado-complementar/pesquisar-por-servico/' + servicoId);
        };

    return {
        buscarTiposAssuntos: buscarTiposAssuntos,
        listarFormularioWeb: listarFormularioWeb,
        mostrarMensagemError: ComumFactory.mostrarMensagemError,
        mostrarMensagemSucesso: ComumFactory.mostrarMensagemSucesso,
        exibirMesagemErroVariosCampos: ComumFactory.exibirMesagemErroVariosCampos,
        exibirRepresentanteLegal:ComumFactory.exibirRepresentanteLegal,
        listarFormatosArquivo: listarFormatosArquivo,
        listarProfissoes: listarProfissoes,
        pesquisarDocumentos: pesquisarDocumentos,
        listarTiposLogradouro: listarTiposLogradouro,
        listarTodasUfs: listarTodasUfs,
        listarTodosNomesMunicipios: listarTodosNomesMunicipios,
        tiposDocumentos: tiposDocumentos,
        mostrarCamposInvalidos: mostrarCamposInvalidos,
        calcularDiasEntreDatas: calcularDiasEntreDatas,
        pesquisarDocumentosComArquivos: pesquisarDocumentosComArquivos,
        buscarIdSistema: buscarIdSistema,
        pesquisarDocumentosUtilizados: pesquisarDocumentosUtilizados,
        htmlToPlaintext: htmlToPlaintext,
        isNumeroInteiro: isNumeroInteiro,
        resertarFormulario: resertarFormulario,
        arrendondarDuasCasasDecimais: arrendondarDuasCasasDecimais,
        formatDate:formatDate,
        pesquisaDadosComplemntar:pesquisaDadosComplemntar
    };
  }
})();

(function () {

  'use strict';

  angular
    .module('su-servicos')
    .factory('arquivoServiceServicos', ['comumServiceServico', 'Upload', '$http', service]);

    function service(comumServiceServico, Upload, $http) {

        this.requerimento;

        var arquivos;

        var finishedBuscar = false;

        var setRequerimento = function(newValue){
            this.requerimento = newValue;
        }

        var getRequerimento = function(){
            return this.requerimento;
        }

        var setArquivos = function(newValue){
            arquivos = newValue;
        }

        var getArquivos = function(){
            return arquivos;
        }

        var setFinishedBuscar = function(estado){
            finishedBuscar = estado;
        }

        var getFinishedBuscar = function(){
            return finishedBuscar;
        }

        var buscarArquivos = function(){
            return $http.get('servicos/api/public/arquivo-requerimento/requerimento-deferido/' + this.requerimento.id);
        }

        var CONTENT_TYPE_PNG = 'image/png';
        var CONTENT_TYPE_TIF = 'image/tif';
        var CONTENT_TYPE_TIFF = 'image/tiff';
        var CONTENT_TYPE_JPEG = 'image/jpeg';
        var CONTENT_TYPE_PDF = 'application/pdf';

        var CONTENT_TYPE_IMAGE = ['image/png', 'image/tif', 'image/jpeg', 'image/jpg'];

        var TAMANHO_MAXIMO_ARQUIVO = 5242880;
        var URL = '/servicos/api/public/arquivo/upload';
        var URL_FILE = '/servicos/api/public/arquivo/upload/file';
        var URL_DOWNLOAD = '/servicos/api/public/arquivo/download/';

        var validarTamanhoMaximoArquivo = function (tamanho) {
            if (tamanho > TAMANHO_MAXIMO_ARQUIVO) {
                var mensagem = comumServiceServico.getMensagemFilter('mensagem-tamanho-max-arquivo');
                ('mensagem-tamanho-max-arquivo');
                comumServiceServico.exibirInfo(mensagem);
                throw mensagem;
            }
        };

        var validarFormatoArquivo = function (formato) {
            if (verificaFormatoValido(formato)) {
                var mensagem = comumServiceServico.mostrarMensagemError('Arquivo em formato inválido.');
                throw mensagem;
            }
        };

      var validarFormatoArquivoAjuda = function (formato) {
        if (verificaFormatoValidoArquivoAjuda(formato)) {
          var mensagem = comumServiceServico.mostrarMensagemError('Arquivo em formato inválido. Os formatos válidos são: PDF, TIF, PNG e JPEG.');
          throw mensagem;
        }
      };

        var verificaFormatoValido = function (formato) {
            var formatoInvalido = true;
            if (formato == CONTENT_TYPE_PDF) {
                formatoInvalido = false;
            }
            return formatoInvalido;
        };

        var verificaFormatoValidoArquivoAjuda = function (formato) {
          var formatoInvalido = false;
          //Git issue #9 --https://git.economia.gov.br/spu/mod-servicos/issues/9
          //if (formato == CONTENT_TYPE_PDF) {
          //  formatoInvalido = false;
          //} else if (formato == CONTENT_TYPE_TIFF) {
          //  formatoInvalido = false;
          //} else if (formato == CONTENT_TYPE_TIF) {
          //  formatoInvalido = false;
          //} else if (formato == CONTENT_TYPE_PNG) {
          // formatoInvalido = false;
          //} else if (formato == CONTENT_TYPE_JPEG) {
          //  formatoInvalido = false;
          //}
          return formatoInvalido;
        };

        var validarExtensaoImagem = function ($file) {
            return CONTENT_TYPE_IMAGE.indexOf($file.type) == -1;
        };

        var verificarArquivosObrigatoriosInformados = function (arquivos) {
            var documentoInformados = [];
            angular.forEach(arquivos, function (item) {

                if (item.files && item.files.length > 0) {
                    documentoInformados.push({id: item.id, possuiArquivo: true});
                }
            });

            var quantidadeDocumentosInformados = documentoInformados.filter(function (item) {
                return item.possuiArquivo == true
            }).length;

            if (quantidadeDocumentosInformados != arquivos.length || quantidadeDocumentosInformados == 0) {
                var mensagem = comumServiceServico.getMensagemFilter('mensagem-documentos-obrigatorios');
                comumServiceServico.exibirInfo(mensagem);
                throw mensagem;
            }
        };

        var addArquivo = function ($file, servico) {

            if (!servico.arquivo) {
                servico.arquivo = {};
            }

            if ($file) {
                validarFormatoArquivo($file.type);
                validarTamanhoMaximoArquivo($file.size);

                Upload.upload({url: URL, data: {file: $file}}).then(function (response) {
                    $file.id = response.data.resposta[0];
                    servico.arquivo = $file;
                });
            }

        };

      var addArquivoAjuda = function ($file, servico) {

        if (!servico.arquivo) {
          servico.arquivo = {};
        }

        if ($file) {
          validarFormatoArquivoAjuda($file[0].type);
          validarTamanhoMaximoArquivo($file[0].size);

          for(var i=0; i<$file.length; i++){
            Upload.upload({url: URL_FILE, data: {file: $file[i]}}).then(function (response) {
              servico.push( response.data.resposta[0]);
            });
          }
        }

      };

        var addArquivoSemValidar = function ($file, arquivos) {
            if ($file) {
                 if (!arquivos) {
                    arquivos = [];
                }

                for(var i=0; i<$file.length; i++){
                    Upload.upload({url: URL_FILE, data: {file: $file[i]}}).then(function (response) {
                        arquivos.push( response.data.resposta[0]);
                    });
                }
            }

        };

        var adicionar = function ($file, arquivos) {
            if (!arquivos) {
                arquivos = [];
            }

            if ($file) {
                validarFormatoArquivo($file.type);
                validarTamanhoMaximoArquivo($file.size);
                Upload.upload({url: URL, data: {file: $file}}).then(function (response) {
                    $file.id = response.data.resposta[0];
                    arquivos.push($file);
                });
            }
        };


        var limparArquivosCarregados = function (arquivos) {

            angular.forEach(arquivos, function (item) {
                item.files = [];
            });

        };

        var prepararDadosArquivoRequerimento = function (documentos) {
            var arquivosRequerimento = [];
            angular.forEach(documentos, function (documento) {
                if (documento.files) {
                    angular.forEach(documento.files, function (file) {
                        arquivosRequerimento.push({idDocumento: documento.id, idArquivo: file.id});
                    });
                }
            });
            return arquivosRequerimento;
        };

        var prepararPlantasEnvio = function (plantas) {
            var plantasEnvio = [];
            if (plantas) {
                angular.forEach(plantas, function (file) {
                    plantasEnvio.push({id: file.id});
                });
            }
            return plantasEnvio;
        };

        var apagar = function (id) {
            return $http.delete('servicos/api/public/arquivo/' + id);
        };

        var apagarServico = function (id) {
            return $http.delete('api/public/servico/arquivo/' + id);
        };

        var uploadArquivo = function($file){
            return Upload.upload({
                url: URL,
                data: {
                    file: $file
                }
            });
        };

      var uploadRetornarArquivo = function($file){
        return Upload.upload({
          url: URL + '/file',
          data: {
            file: $file
          }
        });
      };

        var download = function (id) {
            return URL_DOWNLOAD + id;
        };

        var uploadCallback = function ($file, callback) {
            if ($file) {
                Upload.upload({url: URL_UPLOAD_FILE, data: {file: $file}}).then(function (response) {
                    callback(response.data.resposta[0]);
                });
            }
        };


        return {
            validarTamanhoMaximoArquivo: validarTamanhoMaximoArquivo,
            validarFormatoArquivo: validarFormatoArquivo,
            verificarArquivosObrigatoriosInformados: verificarArquivosObrigatoriosInformados,
            addArquivo: addArquivo,
            addArquivoAjuda: addArquivoAjuda,
            addArquivoSemValidar: addArquivoSemValidar,
            limparArquivosCarregados: limparArquivosCarregados,
            prepararDadosArquivoRequerimento: prepararDadosArquivoRequerimento,
            apagar: apagar,
            apagarServico: apagarServico,
            adicionar: adicionar,
            prepararPlantasEnvio: prepararPlantasEnvio,
            uploadArquivo: uploadArquivo,
            uploadRetornarArquivo: uploadRetornarArquivo,
            validarExtensaoImagem: validarExtensaoImagem,
            download: download,
            uploadCallback:uploadCallback,
            getRequerimento: getRequerimento,
            setRequerimento: setRequerimento,
            getArquivos: getArquivos,
            setArquivos: setArquivos,
            buscarArquivos: buscarArquivos,
            getFinishedBuscar: getFinishedBuscar,
            setFinishedBuscar: setFinishedBuscar
        };

    };


})();

/**
 * Created by Basis Tecnologia on 15/06/2016.
 */
(function () {
    'use strict';

angular.module('su-servicos').filter('PagoNaoPago', function() {
    return function(input) {
        if (input)
            return input ? 'Pago' : 'Não Pago';
    }
});

angular.module('su-servicos').filter('rip', function() {
    return function(input) {
        if (!input)
            return;
        return  input.toString().substr(0,4) + '.' + input.toString().substr(4,7) + '-' +input.toString().substr(11,2);
    };
});

angular.module('su-servicos').filter('cep', function() {
    return function(input) {
        if (!input)
            return;
        return  input.toString().substr(0,5) + '-' + input.toString().substr(5,3);
    };
});

angular.module("su-servicos").filter('cpfcnpj', function() {
    return function(input){
        if (!input) {
            return;
        } else if(input.toString().length <= 11 && input.toString().length > 3) {
            while(input.toString().length < 11){ input = '0'+input }
            return input.toString().substr(0,3) + '.' + input.toString().substr(3,3) + '.' + input.toString().substr(6,3) + '-' + input.toString().substr(9,2);
        }
        else {
            while(input.toString().length < 14){ input = '0'+input }
            return input.toString().substr(0,2) + '.' + input.toString().substr(2,3) + '.' + input.toString().substr(5,3) + '/' + input.toString().substr(8,4) + '-' + input.toString().substr(12,2);
        }
    };
});

angular.module('su-servicos').filter('telefonebr', function() {
    return function (input){
        if (!input) {
          return 'Não informado'
        } if (input.toString().length == 11) {
            return '(' + input.toString().substr(0, 2) + ') ' + input.toString().substr(2, 5) + '-' + input.toString().substr(7, 4);
        } else if (input.toString().length == 10){
            return '(' + input.toString().substr(0, 2) + ') ' + input.toString().substr(2, 4) + '-' + input.toString().substr(6, 4);
        }
    };
});


angular.module('su-servicos').filter('dataformatada', function() {
    return function (input){
        if(!input){
            return ;
        }

        var data = new Date(input);
        var dia = data.getDate();
        if (dia.toString().length == 1)
            dia = "0"+dia;
        var mes = data.getMonth()+1;
        if (mes.toString().length == 1)
            mes = "0"+mes;
        var ano = data.getFullYear();
        return dia+"/"+mes+"/"+ano;
    };
});

  angular.module('su-servicos').filter('simNao', function () {
    return function (input) {
      return input ? 'Sim' : 'Não';
    };
  });


  angular.module('su-servicos')
    .filter('to_trusted', ['$sce', function($sce){
        return function(text) {
            return $sce.trustAsHtml(text);
        };
    }]);

})();

//  https://cdn.gitcdn.link/cdn/angular/bower-material/v1.1.0-rc1/angular-material.js
//  https://github.com/angular/material/blob/master/src/components/datepicker/datePicker.js

(function() {
  'use strict';

  // POST RELEASE
  // TODO(jelbourn): Demo that uses moment.js
  // TODO(jelbourn): make sure this plays well with validation and ngMessages.
  // TODO(jelbourn): calendar pane doesn't open up outside of visible viewport.
  // TODO(jelbourn): forward more attributes to the internal input (required, autofocus, etc.)
  // TODO(jelbourn): something better for mobile (calendar panel takes up entire screen?)
  // TODO(jelbourn): input behavior (masking? auto-complete?)
  // TODO(jelbourn): UTC mode
  // TODO(jelbourn): RTL


  DatePickerCtrl.$inject = ["$scope", "$element", "$attrs", "$compile", "$timeout", "$window", "$mdConstant", "$mdTheming", "$mdUtil", "$mdDateLocale", "$$mdDateUtil", "$$rAF"];
  angular.module('material.components.datepicker')
      .directive('mdDatepickerCustomServico', datePickerDirectiveServico);

  /**
   * @ngdoc directive
   * @name mdDatepickerCustomServico
   * @module material.components.datepicker
   *
   * @param {Date} ng-model The component's model. Expects a JavaScript Date object.
   * @param {expression=} ng-change Expression evaluated when the model value changes.
   * @param {Date=} md-min-date Expression representing a min date (inclusive).
   * @param {Date=} md-max-date Expression representing a max date (inclusive).
   * @param {(function(Date): boolean)=} md-date-filter Function expecting a date and returning a boolean whether it can be selected or not.
   * @param {String=} md-placeholder The date input placeholder value.
   * @param {boolean=} ng-disabled Whether the datepicker is disabled.
   * @param {boolean=} ng-required Whether a value is required for the datepicker.
   *
   * @description
   * `<md-datepicker>` is a component used to select a single date.
   * For information on how to configure internationalization for the date picker,
   * see `$mdDateLocaleProvider`.
   *
   * This component supports [ngMessages](https://docs.angularjs.org/api/ngMessages/directive/ngMessages).
   * Supported attributes are:
   * * `required`: whether a required date is not set.
   * * `mindate`: whether the selected date is before the minimum allowed date.
   * * `maxdate`: whether the selected date is after the maximum allowed date.
   *
   * @usage
   * <hljs lang="html">
   *   <md-datepicker ng-model="birthday"></md-datepicker>
   * </hljs>
   *
   */
  function datePickerDirectiveServico() {
    return {
      template:
          // Buttons are not in the tab order because users can open the calendar via keyboard
          // interaction on the text input, and multiple tab stops for one component (picker)
          // may be confusing.
          '<md-button class="md-datepicker-button md-icon-button" type="button" ' +
              'tabindex="-1" aria-hidden="true" ' +
              'ng-click="ctrl.openCalendarPane($event)" aria-label="Botao data">' +
            '<ng-md-icon class="md-datepicker-calendar-icon" icon="today" style="fill:#788489"></ng-md-icon>' +
          '</md-button>' +
          '<div class="md-datepicker-input-container" ' +
              'ng-class="{\'md-datepicker-focused\': ctrl.isFocused}">' +
            '<input class="md-datepicker-input" aria-haspopup="true" ' +
                'ng-focus="ctrl.setFocused(true)" ng-blur="ctrl.setFocused(false)" ng-model="ctrl.defaultDate" mask="{{::ctrl.mask}}" mask-clean="{{::ctrl.cleanMask}}" mask-restrict="{{::ctrl.restrict}}">' +
            '<md-button type="button" md-no-ink ' +
                'class="md-datepicker-triangle-button md-icon-button" ' +
                'ng-click="ctrl.openCalendarPane($event)" ' +
                'aria-label="{{::ctrl.dateLocale.msgOpenCalendar}}">' +
              '<div class="md-datepicker-expand-triangle"></div>' +
            '</md-button>' +
          '</div>' +

          // This pane will be detached from here and re-attached to the document body.
          '<div class="md-datepicker-calendar-pane md-whiteframe-z1">' +
            '<div class="md-datepicker-input-mask">' +
              '<div class="md-datepicker-input-mask-opaque"></div>' +
            '</div>' +
            '<div class="md-datepicker-calendar">' +
              '<md-calendar role="dialog" aria-label="{{::ctrl.dateLocale.msgCalendar}}" ' +
                  'md-min-date="ctrl.minDate" md-max-date="ctrl.maxDate"' +
                  'md-date-filter="ctrl.dateFilter"' +
                  'ng-model="ctrl.date" ng-if="ctrl.isCalendarOpen">' +
              '</md-calendar>' +
            '</div>' +
          '</div>',
      require: ['ngModel', 'mdDatepickerCustomServico', '?^mdInputContainer'],
      scope: {
        minDate: '=mdMinDate',
        maxDate: '=mdMaxDate',
        placeholder: '@mdPlaceholder',
        dateFilter: '=mdDateFilter',
        //  Custom attributes added
        defaultDate: '@mdCustomDefaultDate',
        mask: '@mdCustomMask',
        cleanMask: '@mdCustomCleanMask',
        restrict: '@mdCustomRestrict',
        required: '@mdCustomRequired'
      },      
      controller: DatePickerCtrl,
      controllerAs: 'ctrl',
      bindToController: true,
      link: function(scope, element, attr, controllers) {
        var ngModelCtrl = controllers[0];
        var mdDatePickerCtrl = controllers[1];

        var mdInputContainer = controllers[2];
        if (mdInputContainer) {
          throw Error('md-datepicker should not be placed inside md-input-container.');
        }

        mdDatePickerCtrl.configureNgModel(ngModelCtrl);
      }
    };
  }

  /** Additional offset for the input's `size` attribute, which is updated based on its content. */
  var EXTRA_INPUT_SIZE = 3;

  /** Class applied to the container if the date is invalid. */
  var INVALID_CLASS = 'md-datepicker-invalid';

  /** Default time in ms to debounce input event by. */
  var DEFAULT_DEBOUNCE_INTERVAL = 500;

  /**
   * Height of the calendar pane used to check if the pane is going outside the boundary of
   * the viewport. See calendar.scss for how $md-calendar-height is computed; an extra 20px is
   * also added to space the pane away from the exact edge of the screen.
   *
   *  This is computed statically now, but can be changed to be measured if the circumstances
   *  of calendar sizing are changed.
   */
  var CALENDAR_PANE_HEIGHT = 368;

  /**
   * Width of the calendar pane used to check if the pane is going outside the boundary of
   * the viewport. See calendar.scss for how $md-calendar-width is computed; an extra 20px is
   * also added to space the pane away from the exact edge of the screen.
   *
   *  This is computed statically now, but can be changed to be measured if the circumstances
   *  of calendar sizing are changed.
   */
  var CALENDAR_PANE_WIDTH = 360;

  /**
   * Controller for md-datepicker.
   *
   * @ngInject @constructor
   */
  function DatePickerCtrl($scope, $element, $attrs, $compile, $timeout, $window,
      $mdConstant, $mdTheming, $mdUtil, $mdDateLocale, $$mdDateUtil, $$rAF) {
    /** @final */
    this.$compile = $compile;

    /** @final */
    this.$timeout = $timeout;

    /** @final */
    this.$window = $window;

    /** @final */
    this.dateLocale = $mdDateLocale;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.$mdConstant = $mdConstant;

    /* @final */
    this.$mdUtil = $mdUtil;

    /** @final */
    this.$$rAF = $$rAF;

    /**
     * The root document element. This is used for attaching a top-level click handler to
     * close the calendar panel when a click outside said panel occurs. We use `documentElement`
     * instead of body because, when scrolling is disabled, some browsers consider the body element
     * to be completely off the screen and propagate events directly to the html element.
     * @type {!angular.JQLite}
     */
    this.documentElement = angular.element(document.documentElement);

    /** @type {!angular.NgModelController} */
    this.ngModelCtrl = null;

    /** @type {HTMLInputElement} */
    this.inputElement = $element[0].querySelector('input');

    /** @final {!angular.JQLite} */
    this.ngInputElement = angular.element(this.inputElement);

    /** @type {HTMLElement} */
    this.inputContainer = $element[0].querySelector('.md-datepicker-input-container');

    /** @type {HTMLElement} Floating calendar pane. */
    this.calendarPane = $element[0].querySelector('.md-datepicker-calendar-pane');

    /** @type {HTMLElement} Calendar icon button. */
    this.calendarButton = $element[0].querySelector('.md-datepicker-button');

    /**
     * Element covering everything but the input in the top of the floating calendar pane.
     * @type {HTMLElement}
     */
    this.inputMask = $element[0].querySelector('.md-datepicker-input-mask-opaque');

    /** @final {!angular.JQLite} */
    this.$element = $element;

    /** @final {!angular.Attributes} */
    this.$attrs = $attrs;

    /** @final {!angular.Scope} */
    this.$scope = $scope;

    /** @type {Date} */
    this.date = null;

    /** @type {boolean} */
    this.isFocused = false;

    /** @type {boolean} */
    this.isDisabled;
    this.setDisabled($element[0].disabled || angular.isString($attrs['disabled']));

    /** @type {boolean} Whether the date-picker's calendar pane is open. */
    this.isCalendarOpen = false;

    /**
     * Element from which the calendar pane was opened. Keep track of this so that we can return
     * focus to it when the pane is closed.
     * @type {HTMLElement}
     */
    this.calendarPaneOpenedFrom = null;

    this.calendarPane.id = 'md-date-pane' + $mdUtil.nextUid();

    $mdTheming($element);

    /** Pre-bound click handler is saved so that the event listener can be removed. */
    this.bodyClickHandler = angular.bind(this, this.handleBodyClick);

    /** Pre-bound resize handler so that the event listener can be removed. */
    this.windowResizeHandler = $mdUtil.debounce(angular.bind(this, this.closeCalendarPane), 100);

    // Unless the user specifies so, the datepicker should not be a tab stop.
    // This is necessary because ngAria might add a tabindex to anything with an ng-model
    // (based on whether or not the user has turned that particular feature on/off).
    if (!$attrs['tabindex']) {
      $element.attr('tabindex', '-1');
    }

    this.installPropertyInterceptors();
    this.attachChangeListeners();
    this.attachInteractionListeners();

    var self = this;
    $scope.$on('$destroy', function() {
      self.detachCalendarPane();
    });
  }

  /**
   * Sets up the controller's reference to ngModelController.
   * @param {!angular.NgModelController} ngModelCtrl
   */
  DatePickerCtrl.prototype.configureNgModel = function(ngModelCtrl) {
    this.ngModelCtrl = ngModelCtrl;

    var self = this;
    ngModelCtrl.$render = function() {
      var value = self.ngModelCtrl.$viewValue;

      if (value && !(value instanceof Date)) {
        throw Error('The ng-model for md-datepicker must be a Date instance. ' +
            'Currently the model is a: ' + (typeof value));
      }

      self.date = value;
      self.inputElement.value = self.dateLocale.formatDate(value);
      self.resizeInputElement();
      self.updateErrorState();
    };
  };

  /**
   * Attach event listeners for both the text input and the md-calendar.
   * Events are used instead of ng-model so that updates don't infinitely update the other
   * on a change. This should also be more performant than using a $watch.
   */
  DatePickerCtrl.prototype.attachChangeListeners = function() {
    var self = this;

    self.$scope.$on('md-calendar-change', function(event, date) {
      self.ngModelCtrl.$setViewValue(date);
      self.date = date;
      self.inputElement.value = self.dateLocale.formatDate(date);
      self.closeCalendarPane();
      self.resizeInputElement();
      self.updateErrorState();
    });

    self.ngInputElement.on('input', angular.bind(self, self.resizeInputElement));
    // TODO(chenmike): Add ability for users to specify this interval.
    self.ngInputElement.on('input', self.$mdUtil.debounce(self.handleInputEvent,
        DEFAULT_DEBOUNCE_INTERVAL, self));
  };

  /** Attach event listeners for user interaction. */
  DatePickerCtrl.prototype.attachInteractionListeners = function() {
    var self = this;
    var $scope = this.$scope;
    var keyCodes = this.$mdConstant.KEY_CODE;

    // Add event listener through angular so that we can triggerHandler in unit tests.
    self.ngInputElement.on('keydown', function(event) {
      if (event.altKey && event.keyCode == keyCodes.DOWN_ARROW) {
        self.openCalendarPane(event);
        $scope.$digest();
      }
    });

    $scope.$on('md-calendar-close', function() {
      self.closeCalendarPane();
    });
  };

  /**
   * Capture properties set to the date-picker and imperitively handle internal changes.
   * This is done to avoid setting up additional $watches.
   */
  DatePickerCtrl.prototype.installPropertyInterceptors = function() {
    var self = this;

    if (this.$attrs['ngDisabled']) {
      // The expression is to be evaluated against the directive element's scope and not
      // the directive's isolate scope.
      var scope = this.$scope.$parent;

      if (scope) {
        scope.$watch(this.$attrs['ngDisabled'], function(isDisabled) {
          self.setDisabled(isDisabled);
        });
      }
    }

    Object.defineProperty(this, 'placeholder', {
      get: function() { return self.inputElement.placeholder; },
      set: function(value) { self.inputElement.placeholder = value || ''; }
    });
  };

  /**
   * Sets whether the date-picker is disabled.
   * @param {boolean} isDisabled
   */
  DatePickerCtrl.prototype.setDisabled = function(isDisabled) {
    this.isDisabled = isDisabled;
    this.inputElement.disabled = isDisabled;
    this.calendarButton.disabled = isDisabled;
  };

  /**
   * Sets the custom ngModel.$error flags to be consumed by ngMessages. Flags are:
   *   - mindate: whether the selected date is before the minimum date.
   *   - maxdate: whether the selected flag is after the maximum date.
   *   - filtered: whether the selected date is allowed by the custom filtering function.
   *   - valid: whether the entered text input is a valid date
   *
   * The 'required' flag is handled automatically by ngModel.
   *
   * @param {Date=} opt_date Date to check. If not given, defaults to the datepicker's model value.
   */
  DatePickerCtrl.prototype.updateErrorState = function(opt_date) {
    var date = opt_date || this.date;

    // Clear any existing errors to get rid of anything that's no longer relevant.
    this.clearErrorState();

    if (date == undefined) return;

    if (this.dateUtil.isValidDate(date)) {
      // Force all dates to midnight in order to ignore the time portion.
      date = this.dateUtil.createDateAtMidnight(date);

      if (this.dateUtil.isValidDate(this.minDate)) {
        var minDate = this.dateUtil.createDateAtMidnight(this.minDate);
        this.ngModelCtrl.$setValidity('mindate', date >= minDate);
      }

      if (this.dateUtil.isValidDate(this.maxDate)) {
        var maxDate = this.dateUtil.createDateAtMidnight(this.maxDate);
        this.ngModelCtrl.$setValidity('maxdate', date <= maxDate);
      }

      if (angular.isFunction(this.dateFilter)) {
        this.ngModelCtrl.$setValidity('filtered', this.dateFilter(date));
      }
    } else {
      // The date is seen as "not a valid date" if there is *something* set
      // (i.e.., not null or undefined), but that something isn't a valid date.
      //  this.ngModelCtrl.$setValidity('valid', date == null);
      this.ngModelCtrl.$setValidity('valid', !isNaN(date));
      this.ngModelCtrl.$setValidity('required', this.required !== "true" || date !== null);
    }

    // TODO(jelbourn): Change this to classList.toggle when we stop using PhantomJS in unit tests
    // because it doesn't conform to the DOMTokenList spec.
    // See https://github.com/ariya/phantomjs/issues/12782.
    if (!this.ngModelCtrl.$valid) {
      this.inputContainer.classList.add(INVALID_CLASS);
    }
  };

  /** Clears any error flags set by `updateErrorState`. */
  DatePickerCtrl.prototype.clearErrorState = function() {
    this.inputContainer.classList.remove(INVALID_CLASS);
    ['mindate', 'maxdate', 'filtered', 'valid', 'required'].forEach(function(field) {
      this.ngModelCtrl.$setValidity(field, true);
    }, this);
  };

  /** Resizes the input element based on the size of its content. */
  DatePickerCtrl.prototype.resizeInputElement = function() {
    this.inputElement.size = this.inputElement.value.length + EXTRA_INPUT_SIZE;
  };

  /**
   * Sets the model value if the user input is a valid date.
   * Adds an invalid class to the input element if not.
   */
  DatePickerCtrl.prototype.handleInputEvent = function() {
    var inputString = this.inputElement.value;
    var parsedDate = inputString ? this.dateLocale.parseDate(inputString) : null;
    this.dateUtil.setDateTimeToMidnight(parsedDate);

    // An input string is valid if it is either empty (representing no date)
    // or if it parses to a valid date that the user is allowed to select.
    var isValidInput = inputString == '' || (
      this.dateUtil.isValidDate(parsedDate) &&
      this.dateLocale.isDateComplete(inputString) &&
      this.isDateEnabled(parsedDate)
    );

    // The datepicker's model is only updated when there is a valid input.
    if (isValidInput) {
      this.ngModelCtrl.$setViewValue(parsedDate);
      this.date = parsedDate;
    }

    this.updateErrorState(parsedDate);
  };

  /**
   * Check whether date is in range and enabled
   * @param {Date=} opt_date
   * @return {boolean} Whether the date is enabled.
   */
  DatePickerCtrl.prototype.isDateEnabled = function(opt_date) {
    return this.dateUtil.isDateWithinRange(opt_date, this.minDate, this.maxDate) &&
          (!angular.isFunction(this.dateFilter) || this.dateFilter(opt_date));
  };

  /** Position and attach the floating calendar to the document. */
  DatePickerCtrl.prototype.attachCalendarPane = function() {
    var calendarPane = this.calendarPane;
    calendarPane.style.transform = '';
    this.$element.addClass('md-datepicker-open');

    var elementRect = this.inputContainer.getBoundingClientRect();
    var bodyRect = document.body.getBoundingClientRect();

    // Check to see if the calendar pane would go off the screen. If so, adjust position
    // accordingly to keep it within the viewport.
    var paneTop = elementRect.top - bodyRect.top;
    var paneLeft = elementRect.left - bodyRect.left;

    // If ng-material has disabled body scrolling (for example, if a dialog is open),
    // then it's possible that the already-scrolled body has a negative top/left. In this case,
    // we want to treat the "real" top as (0 - bodyRect.top). In a normal scrolling situation,
    // though, the top of the viewport should just be the body's scroll position.
    var viewportTop = (bodyRect.top < 0 && document.body.scrollTop == 0) ?
        -bodyRect.top :
        document.body.scrollTop;

    var viewportLeft = (bodyRect.left < 0 && document.body.scrollLeft == 0) ?
        -bodyRect.left :
        document.body.scrollLeft;

    var viewportBottom = viewportTop + this.$window.innerHeight;
    var viewportRight = viewportLeft + this.$window.innerWidth;

    // If the right edge of the pane would be off the screen and shifting it left by the
    // difference would not go past the left edge of the screen. If the calendar pane is too
    // big to fit on the screen at all, move it to the left of the screen and scale the entire
    // element down to fit.
    if (paneLeft + CALENDAR_PANE_WIDTH > viewportRight) {
      if (viewportRight - CALENDAR_PANE_WIDTH > 0) {
        paneLeft = viewportRight - CALENDAR_PANE_WIDTH;
      } else {
        paneLeft = viewportLeft;
        var scale = this.$window.innerWidth / CALENDAR_PANE_WIDTH;
        calendarPane.style.transform = 'scale(' + scale + ')';
      }

      calendarPane.classList.add('md-datepicker-pos-adjusted');
    }

    // If the bottom edge of the pane would be off the screen and shifting it up by the
    // difference would not go past the top edge of the screen.
    if (paneTop + CALENDAR_PANE_HEIGHT > viewportBottom &&
        viewportBottom - CALENDAR_PANE_HEIGHT > viewportTop) {
      paneTop = viewportBottom - CALENDAR_PANE_HEIGHT;
      calendarPane.classList.add('md-datepicker-pos-adjusted');
    }

    calendarPane.style.left = paneLeft + 'px';
    calendarPane.style.top = paneTop + 'px';
    document.body.appendChild(calendarPane);

    // The top of the calendar pane is a transparent box that shows the text input underneath.
    // Since the pane is floating, though, the page underneath the pane *adjacent* to the input is
    // also shown unless we cover it up. The inputMask does this by filling up the remaining space
    // based on the width of the input.
    this.inputMask.style.left = elementRect.width + 'px';

    // Add CSS class after one frame to trigger open animation.
    this.$$rAF(function() {
      calendarPane.classList.add('md-pane-open');
    });
  };

  /** Detach the floating calendar pane from the document. */
  DatePickerCtrl.prototype.detachCalendarPane = function() {
    this.$element.removeClass('md-datepicker-open');
    this.calendarPane.classList.remove('md-pane-open');
    this.calendarPane.classList.remove('md-datepicker-pos-adjusted');

    if (this.isCalendarOpen) {
      this.$mdUtil.enableScrolling();
    }

    if (this.calendarPane.parentNode) {
      // Use native DOM removal because we do not want any of the angular state of this element
      // to be disposed.
      this.calendarPane.parentNode.removeChild(this.calendarPane);
    }
  };

  /**
   * Open the floating calendar pane.
   * @param {Event} event
   */
  DatePickerCtrl.prototype.openCalendarPane = function(event) {
    if (!this.isCalendarOpen && !this.isDisabled) {
      this.isCalendarOpen = true;
      this.calendarPaneOpenedFrom = event.target;

      // Because the calendar pane is attached directly to the body, it is possible that the
      // rest of the component (input, etc) is in a different scrolling container, such as
      // an md-content. This means that, if the container is scrolled, the pane would remain
      // stationary. To remedy this, we disable scrolling while the calendar pane is open, which
      // also matches the native behavior for things like `<select>` on Mac and Windows.
      this.$mdUtil.disableScrollAround(this.calendarPane);

      this.attachCalendarPane();
      this.focusCalendar();

      // Attach click listener inside of a timeout because, if this open call was triggered by a
      // click, we don't want it to be immediately propogated up to the body and handled.
      var self = this;
      this.$mdUtil.nextTick(function() {
        // Use 'touchstart` in addition to click in order to work on iOS Safari, where click
        // events aren't propogated under most circumstances.
        // See http://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html
        self.documentElement.on('click touchstart', self.bodyClickHandler);
      }, false);

      window.addEventListener('resize', this.windowResizeHandler);
    }
  };

  /** Close the floating calendar pane. */
  DatePickerCtrl.prototype.closeCalendarPane = function() {
    if (this.isCalendarOpen) {
      this.detachCalendarPane();
      this.isCalendarOpen = false;
      this.calendarPaneOpenedFrom.focus();
      this.calendarPaneOpenedFrom = null;

      this.ngModelCtrl.$setTouched();

      this.documentElement.off('click touchstart', this.bodyClickHandler);
      window.removeEventListener('resize', this.windowResizeHandler);
    }
  };

  /** Gets the controller instance for the calendar in the floating pane. */
  DatePickerCtrl.prototype.getCalendarCtrl = function() {
    return angular.element(this.calendarPane.querySelector('md-calendar')).controller('mdCalendar');
  };

  /** Focus the calendar in the floating pane. */
  DatePickerCtrl.prototype.focusCalendar = function() {
    // Use a timeout in order to allow the calendar to be rendered, as it is gated behind an ng-if.
    var self = this;
    this.$mdUtil.nextTick(function() {
      self.getCalendarCtrl().focus();
    }, false);
  };

  /**
   * Sets whether the input is currently focused.
   * @param {boolean} isFocused
   */
  DatePickerCtrl.prototype.setFocused = function(isFocused) {
    if (!isFocused) {
      this.ngModelCtrl.$setTouched();
    }
    this.isFocused = isFocused;
  };

  /**
   * Handles a click on the document body when the floating calendar pane is open.
   * Closes the floating calendar pane if the click is not inside of it.
   * @param {MouseEvent} event
   */
  DatePickerCtrl.prototype.handleBodyClick = function(event) {
    if (this.isCalendarOpen) {
      // TODO(jelbourn): way want to also include the md-datepicker itself in this check.
      var isInCalendar = this.$mdUtil.getClosest(event.target, 'md-calendar');
      if (!isInCalendar) {
        this.closeCalendarPane();
      }

      this.$scope.$digest();
    }
  };
})();

(function () {

  'use strict';

  angular.module('su-servicos').config(["$stateProvider", function ($stateProvider) {

    $stateProvider
        .state('servicos', {
          abstract: true,
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          url: '/servicos',
          ncyBreadcrumb: {
            label: 'CADASTRAR SERVIÇOS',
            parent: 'INTEGRADOR.HOME'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/app/language');
            }]
          }
        })
        .state('servicos.SERVICOS_CADASTRAR_SERVICOS', {
          url: '/cadastrar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'CADASTRAR SERVIÇOS',
            parent: 'INTEGRADOR.HOME'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/servicos/language');
            }]
          }
        })

        .state('servicos.editarServicos', {
          url: '/editar/:id',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'EDITAR SERVIÇOS',
            parent: 'servicos.SERVICOS_CADASTRAR_SERVICOS'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/servicos/language');
            }]
          }
        })

        .state('servicos.SERVICOS_CONSULTAR_SERVICOS', {
          url: '/consultar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'CONSULTAR SERVIÇOS',
            parent: 'INTEGRADOR.HOME'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/servicos/language');
            }]
          }
        })

        .state('servicos.SERVICOS_TRIAGEM', {
          url: '/requerimentos',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'REQUERIMENTOS',
            parent: 'INTEGRADOR.HOME'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/triagem/language');
            }]
          }
        })

        .state('servicos.analise-simplificada', {
          url: '/analise-simplificada/:idRequerimento',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR ',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/triagem/language');
            }]
          }
        })

        .state('servicos.requerimentoIsencao/detalhar', {
          url: '/requerimentoIsencao/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR ISENÇÃO DE TAXAS ',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-isencao/language');
            }]
          }
        })

        .state('servicos.solicitarPermissaoUso/detalhar', {
          url: '/solicitarPermissaoUso/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR PERMISSÃO DE USO ',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-solicitar-permissao-uso/language');
            }]
          }
        })

        .state('servicos.requerimentoRegularizacaoUso/detalhar', {
          url: '/requerimentoRegularizacaoUso/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR REGULARIZAÇÃO DE USO',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-regularizacao-uso-imovel/language');
            }]
          }
        })

        .state('servicos.requerimentoRevigoracaoAforamento/detalhar', {
          url: '/requerimentoRevigoracaoAforamento/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR REVIGORAÇÃO DE AFORAMENTO',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-revigoracao-aforamento/language');
            }]
          }
        })

        .state('servicos.revisaoValorArea/detalhar', {
          url: '/requerimentoRevisaoValorArea/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR VALOR E AREA',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-revisao-valor-area/language');
            }]
          }
        })

        .state('servicos.consultaDominialidade/detalhar', {
          url: '/requerimentoConsultaDominialidade/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR DOMINIALIDADE',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-consulta-dominialidade/language');
            }]
          }
        })

        .state('servicos.certidaoAforamento/detalhar', {
          url: '/certidaoAforamento/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR CERTIDÃO DE AFORAMENTO ',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-remicao-aforamento/language');
            }]
          }
        })

        .state('servicos.requerimentoTransferenciaTitular/detalhar', {
          url: '/requerimentoTransferenciaTitular/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR TRANSFERÊNCIA DE TITULAR',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-transferencia-titular/language');
            }]
          }
        })

        .state('servicos.requerimentoAutorizacaoObras/detalhar', {
          url: '/requerimentoAutorizacaoObras/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR AUTORIZAÇÃO DE OBRAS',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-consulta-dominialidade/language');
            }]
          }
        })

        .state('servicos.requerimentoCancelamentoCobranca/detalhar', {
          url: '/requerimentoCancelamentoCobranca/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR CANCELAMENTO DE COBRANÇA',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-cancelamento-cobranca/language');
            }]
          }
        })

        .state('servicos.requerimentoCancelamentoUtilizacao/detalhar', {
          url: '/requerimentoCancelamentoUtilizacao/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR CANCELAMENTO DE UTILIZAÇÃO',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-cancelamento-utilizacao/language');
            }]
          }
        })

        .state('servicos.requerimentoMudancaRegime/detalhar', {
          url: '/requerimentoMudancaRegimeContrato/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR MUDANÇA DE REGIME',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          controllerAs: 'portalRedirecionamentoController',
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-mudanca-regime-contrato/language');
            }]
          }
        })

        .state('servicos.servicoGenericoSemRip/detalhar', {
          url: '/servicoGenericoSemRip/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR SERVICO GENÉRICO SEM RIP',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/servico-generico-sem-rip/language');
            }]
          }
        })

        .state('servicos.servicoGenericoComRip/detalhar', {
          url: '/servicoGenericoComRip/detalhar',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR SERVICO GENÉRICO COM RIP',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/servico-generico-com-rip/language');
            }]
          }
        })

        .state('servicos.novaConsultaDominialidade/detalhar', {
          url: '/novaConsultaDominialidade/detalhar',
          templateUrl: 'scripts-servicos/pages/requerimento-nova-consulta-dominialidade/views/detalhar/detalhar.html',
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR NOVA CONSULTA DOMINIALIDADE',
            parent: 'servicos.SERVICOS_TRIAGEM'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/requerimento-nova-consulta-dominialidade/language');
            }]
          }
        })

        .state('servicos.SERVICOS_CONSULTAR_GRUPO_PROCESSO_SEI', {
          url: '/consultargrupoprocessosei',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'CONSULTAR GRUPOS PROCESSOS SEI',
            parent: 'INTEGRADOR.HOME'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/grupo-processo-sei/language');
            }]
          }
        })

        .state('servicos.SERVICOS_CADASTRAR_GRUPO_PROCESSO_SEI', {
          url: '/cadastrargrupoprocessosei',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'CADASTRAR GRUPO PROCESSO SEI',
            parent: 'INTEGRADOR.HOME'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/grupo-processo-sei/language');
            }]
          }
        })

        .state('servicos.SERVICOS_EDITAR_GRUPO_PROCESSO_SEI', {
          url: '/cadastrargrupoprocessosei/:id',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'EDITAR GRUPO PROCESSO SEI',
            parent: 'servicos.SERVICOS_CADASTRAR_GRUPO_PROCESSO_SEI'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/grupo-processo-sei/language');
            }]
          }
        })

        .state('servicos.SERVICOS_DETALHAR_GRUPO_PROCESSO_SEI', {
          url: '/detalhargrupoprocessosei/:id',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR GRUPO PROCESSO SEI',
            parent: 'servicos.SERVICOS_CONSULTAR_GRUPO_PROCESSO_SEI'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/grupo-processo-sei/language');
            }]
          }
        })

        .state('servicos.SERVICOS_CONSULTAR_UNIDADE_SEI', {
          url: '/consultarunidadesei',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'CONSULTAR UNIDADE SEI',
            parent: 'INTEGRADOR.HOME'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/unidade-sei/language');
            }]
          }
        })

        .state('servicos.SERVICOS_CADASTRAR_UNIDADE_SEI', {
          url: '/cadastrarunidadesei',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'CADASTRAR UNIDADE SEI',
            parent: 'INTEGRADOR.HOME'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/unidade-sei/language');
            }]
          }
        })

        .state('servicos.SERVICOS_EDITAR_UNIDADE_SEI', {
          url: '/cadastrarunidadesei/:id',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'EDITAR UNIDADE SEI',
            parent: 'servicos.SERVICOS_CADASTRAR_UNIDADE_SEI'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/unidade-sei/language');
            }]
          }
        })

        .state('servicos.SERVICOS_DETALHAR_UNIDADE_SEI', {
          url: '/detalharunidadesei/:id',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'DETALHAR UNIDADE SEI',
            parent: 'servicos.SERVICOS_CONSULTAR_UNIDADE_SEI'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/unidade-sei/language');
            }]
          }
        })

        .state('servicos.SERVICOS_CONSULTAR_TIPO_PROCESSO_SEI', {
          url: '/consultartipoprocessosei',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'CONSULTAR TIPOS PROCESSOS SEI',
            parent: 'INTEGRADOR.HOME'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/tipo-processo-sei/language');
            }]
          }
        })

        .state('servicos.SERVICOS_CADASTRAR_TIPO_PROCESSO_SEI', {
          url: '/cadastrartipoprocessosei',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'CADASTRAR TIPO PROCESSO SEI',
            parent: 'INTEGRADOR.HOME'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/tipo-processo-sei/language');
            }]
          }
        })

        .state('servicos.SERVICOS_EDITAR_TIPO_PROCESSO_SEI', {
          url: '/cadastrartipoprocessosei/:id',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'EDITAR TIPO PROCESSO SEI',
            parent: 'servicos.SERVICOS_CADASTRAR_TIPO_PROCESSO_SEI'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/tipo-processo-sei/language');
            }]
          }
        })
        .state('servicos.REDIRECIONAR', {
          url: '/portal',
          templateUrl: "scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html",
          controller: 'portalRedirecionamentoController as portalRedirecionamentoController',
          ncyBreadcrumb: {
            label: 'Portal Redirecionamento',
            parent: 'servicos'
          },
          resolve: {
            trans: ["Traducoes", function (Traducoes) {
              return new Traducoes('scripts-servicos/pages/tipo-processo-sei/language');
            }]
          }
        })

    ;

  }]);

})();

(function () {
  'use strict';

  angular.module('su-servicos')

    .config(["localStorageServiceProvider", function (localStorageServiceProvider) {
      localStorageServiceProvider.setPrefix('servicos');
    }])

    .config(['uiMask.ConfigProvider', function (uiMaskConfigProvider){
      uiMaskConfigProvider.addDefaultPlaceholder(false);
    }])

})();

angular.module("su-servicos").run(["$templateCache", function($templateCache) {$templateCache.put("scripts-servicos/app/language/pt-BR.json","{ \"Dados do Requerente\":\"\", \"dados-requerente\":\"DADOS DO INTERESSADO\", \"cpf-cnpj\":\"CPF/CNPJ:\", \"nome-razao-social\":\"Nome/Razão Social:\", \"tipo\":\"Tipo:\", \"tipo-solicitante\":\"Tipo de Solicitante:\", \"cep\":\"CEP:\", \"rip\": \"RIP:\", \"nao-sabe-cep\":\"Não sabe seu CEP?\", \"uf\":\"UF: \", \"municipio\":\"Município: \", \"tipo-logradouro\":\"Tipo Logradouro: \", \"logradouro\":\"Logradouro: \", \"numero\":\"Número: \", \"bairro\":\"Bairro: \", \"complemento\":\"Complemento: \", \"email\":\"E-mail: \", \"repetir-email\":\"Repetir E-mail: \", \"telefone-fixo\":\"Telefone Fixo: \", \"telefone-celular\":\"Telefone Celular: \", \"telefone-comercial\":\"Telefone Comercial: \", \"o-que-voce-esta-procurando\":\"O que você está procurando\", \"falecido\":\"Falecido: \", \"endereco-correspondencia\":\"Utilizar este endereço para correspondência: \", \"dados-correspondencia\": \"ENDEREÇO DE CORRESPONDÊNCIA\", \"dados-representante-legal\":\"DADOS DO REPRESENTANTE LEGAL\", \"cpf\":\"CPF: \", \"nome\":\"Nome: \", \"cargo\":\"Cargo: \", \"natureza\": \"Natureza:\", \"icon-prioridade-atendimento\":\"Requerimento Com Prioridade Atendimento\", \"dados-socioecomica\":\"INFORMAÇÕES SOCIOECONÔMICAS\", \"label-profissao\":\"Profissão\", \"renda-familiar\":\"Renda Familiar Mensal (R$)\", \"possui-dependentes\":\"Possui dependentes economicos\", \"quantos\":\"Quantos\", \"renda\":\"Renda Mensal (R$)\", \"area-abrangencia-campo\":\"Área de Abrangência do evento (m²)\", \"dados-imovel\":\"DADOS DO IMÓVEL\", \"dados-complementar\":\"DADOS COMPLEMENTARES\", \"arquivos-ajuda\": \"ARQUIVOS DE AJUDA\", \"arq-ajuda\": \"ARQ. AJUDA\", \"area-terreno\":\"Área do Terreno(m²)\", \"area-contruida\":\"Área Construída(m²)\", \"area-fracao-ideal-terreno-transferido\":\"Área ou fração ideal do terreno transferido(m²)\", \"imovel-desmembrado\": \"O imóvel foi desmembrado?\", \"descricao\": \"Descrição\", \"tipo-imovel\": \"Tipo do Imóvel\", \"possui-area-construida\": \"Possui área construída?\", \"area-construida\": \"Área Construída(m²)\", \"fotos-imovel\": \"Fotos do Imóvel (Externas do imóvel e das benfeitorias se existentes)\", \"adicionar-foto\": \"Adicionar Foto\", \"incluir-fotos-imovel\": \"Incluir Fotos Imóvel\", \"fotos\": \"Fotos\", \"maximo-foto\": \"São permitidas, no máximo, 4 fotos.\", \"formato-invalido\": \"Formato de arquivo inválido\", \"incluir-duas-fotos\": \"Incluir pelo menos 2 fotos\", \"dados-utilizacao-cancelamento\": \"DADOS DA UTILIZAÇÃO/CANCELAMENTO\", \"selecionar-utilizacao-cancelamento\": \"Selecione a(s) utilização que deseja realizar o cancelamento.\", \"motivo\": \"Motivo\", \"justificativa-cancelamento\": \"Justificativa do Cancelamento\", \"dados-cancelamento-cobranca\": \"DADOS DO CANCELAMENTO\", \"requerimento-transferencia\": \"Requerimento de Transferência de Titular\", \"dados-documentos\":\"Documentos\", \"possuo\":\"Possuo\", \"nao-possuo\":\"Não Possuo\", \"nao-possuo-consegui\":\"Não possuo mas posso conseguir\", \"o-imovel-foi-transferido\": \"O imóvel foi trasnferido para mais de uma pessoa\", \"btn-novo\": \"Novo\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-retornar\": \"Retornar\", \"btn-gravar\": \"Gravar\", \"btn-adiconar-documento\": \"Adicionar Documento\", \"btn-editar-documento\": \"Editar Documento\", \"btn-fechar\": \"Fechar\", \"btn-inserir\": \"Inserir\", \"btn-alterar\": \"Alterar\", \"btn-confirmar\": \"Confirmar\", \"btn-incluir\": \"Incluir\", \"btn-enviar\": \"Enviar\", \"btn-inicializar-analise\": \"Iniciar Análise\", \"btn-inicializar-triagem\": \"Iniciar Triagem\", \"btn-adicionar\": \"Adicionar\", \"rb-sim\": \"Sim\", \"rb-nao\": \"Não\", \"rb-autuar-processo\": \"Autuar processo\", \"rb-concluir-processo\": \"Concluir serviço\", \"rb-retornar-pendencia\": \"Retornar pendência para o Interessado\", \"rb-cancelar-requerimento\": \"Cancelar requerimento\", \"rb-processo-existente\": \"Processo existente\", \"rb-criar-novo-processo\": \"Criar novo processo\", \"rb-concluir-deferimento\": \"Concluir com Deferimento\", \"rb-concluir-indeferimento\": \"Concluir com Indeferimento\", \"rb-concluir-na-triagem\": \"Concluído na Triagem\", \"label-descricao\": \"Descrição\", \"label-justificativa\": \"Justificativa\", \"label-nome\": \"Nome\", \"label-acoes\": \"Ações\", \"label-tipo\": \"Tipo\", \"label-tipo-documento\": \"Tipo de Documento\", \"label-avaliacao-tecnica\": \"Triagem\", \"label-data\": \"Data\", \"label-situacao\": \"Situação\", \"label-responsavel\": \"Responsável\", \"label-justificativa-anotacao\": \"Justificativa/Anotação\", \"label-historico\": \"Histórico\", \"label-analise-previa-check-list\": \"Triagem (Checklist)\", \"label-analise-tecnica-check-list\": \"Análise Técnica (Checklist)\", \"label-atesto-devidos-fins\": \"Atesto para os devidos fins que:\", \"label-documento-adicionados-requerimento\": \"Os documentos adicionados no requerimento enviado são considerados válidos para a abertura do processo\", \"label-obrigatorios\": \"Obrigatórios\", \"label-obrigatorio\": \"Obrigatório\", \"label-complementares\": \"Complementares\", \"label-numero-processo\": \"Nº processo\", \"label-anotacoes-complementares\": \"Anotações Complementares\", \"label-anotacoes\": \"Anotações\", \"label-cpf-cnpj\": \"CPF/CNPJ\", \"label-nao-informado\":\"Não Informado\", \"label-objetivo-requerimento\": \"Objetivo do Requerimento\", \"label-descricao-objetivo\": \"Descrição objetivo\", \"label-localizacao-imovel\": \"Localização do Imóvel\", \"label-fotos-imovel\":\"Fotos do Imóvel\", \"label-renda-familiar-mensal\": \"Renda familiar mensal (R$): \", \"label-cpf\": \"CPF\", \"label-renda-mensal\": \"Renda Mensal\", \"label-extensao\":\"Adicionar outra Extensão\", \"label-analise-tecnica\" : \"Análise Tecnica\", \"label-documentos\" : \"Documentos\", \"label-arquivos-do-documento\": \"Arquivos\", \"label-nome-arquivo\": \"Nome do Arquivo\", \"label-planta\": \"Planta\", \"label-acao\": \"Ação\", \"label-documentos-obrigatorios\": \"Documentos Obrigatórios\", \"label-documentos-complementares\": \"Documentos Complementares\", \"label-outro-documentos\": \"Outros Documentos\", \"label-consulta-dominialidade\": \"Consulta de Dominialidade\", \"label-visualizar-imagem\": \"Visualizar Imagem\", \"label-dados-regime-contrato\": \"Dados do Regime/Contrato\", \"label-regime-contrato\": \"Regime/Contrato Pretendido\", \"label-uso-pretendido\": \"Uso Pretendido\", \"label-descricao-uso\": \"Descrição Uso\", \"label-confirmacao\":\"Confirmação\", \"label-atencao\":\"Atenção\", \"label-cartorio-registro-imoveis\": \"O interessado é cartório de registro de imóveis?\", \"prioridade-atendimento\":\"Você declara que necessita de algum tipo de priorização no seu atendimento? Informe qual.\", \"label-prioridade-atendimento\":\"Prioridade no Atendimento\", \"label-idosos-idade-entre\":\"Idoso com idade acima de 60 anos e menor que 80 anos\", \"label-idosos-idade-acima\":\"Idosos com idade acima de 80 anos\", \"label-doencas-enquadradas\":\"Pessoas com deficiência, gestantes, lactantes, pessoas com crianças de colo e os obesos\", \"label-upload-relatorio-medico\":\"Upload de Relatório Médico\", \"tooltip-limpar-campos\": \"Limpar\", \"tooltip-pesquisar\": \"Pesquisar\", \"tooltip-download\": \"Download\", \"msg-validacao-justificativa\" : \"Informar anotações dos itens marcados como \'não\'\", \"msg-checklist-nao-preenchido\" : \"Informar \'sim\' ou \'não\' em todos os itens do checklist\", \"msg-campos-obrigatorios\" : \"Preencha os campos obrigatórios.\", \"msg-analise-salva-sucesso\" : \"Análise salva com sucesso.\", \"msg-autuacao-processo\" : \"Autuação Processo\", \"msg-num-processo-existente\" : \"Número do Processo existente\", \"msg-concluir-atendimento\": \"Atendimento concluído com sucesso\", \"msg-retornar-pendencias\": \"Pendência retornada com sucesso\", \"msg-retornar-cancelamento\": \"Requerimento cancelado com sucesso\", \"msg-adicionar-arquivo-concluir-atendimento\": \"Necessário adicionar pelo menos um arquivo.\", \"label-name-anotacoes-complementares\": \"Anotações Complementares\", \"btn-inserir-documento\": \"Inserir Documento\", \"msg-concluir-atendimento-documento-obrigatorio\": \"Obrigatório informar um documento se selecionada as opções de \'Concluir com Deferimento\' ou \'Concluir com Indeferimento\'\", \"solicitante\": \"Solicitante\", \"analise-tecnica\": \"Análise Técnica\", \"triagem\": \"Triagem\", \"checklist-analise-tecnica\": \"Checklist de Análise Técnica\", \"checklist-triagem\": \"Checklist Triagem\", \"declaracoes\": \"Declarações\", \"erro-campo-duplicado\": \"{{campo}} já encontra-se cadastrado.\", \"dados-do-adquirente-titular\": \"DADOS DO ADQUIRENTE TITULAR\", \"label-dados-utilizacao\": \"Dados da Utilização\", \"label-deseja-revisar\": \"O que deseja revisar?\", \"label-instrumento-destinacao\": \"Instrumento de Destinação\", \"label-vigencia\": \"Vigência\", \"cbx-valor-cobranca\": \"Valor Cobrança\", \"cbx-area-fracao-ideal\": \"Área/Fração Ideal\", \"fracao-ideal\": \"Fração Ideal(m²)\", \"utilizacao-atual\": \"Utilização Atual\", \"objetivo-da-transferencia\": \"Objetivo da Transferência\", \"dados-formaAquisicao\" : \"FORMAS DE AQUISIÇÃO\", \"valorEstimado\" : \"Valor Estimado:\", \"formaPagamento\" : \"Forma de Pagamento:\", \"label-item\": \"Item\", \"excluir-item-checklist-analise-tecnica\": \"Excluir item de Checklist Análise Técnica\", \"excluir-item-checklist-triagem\": \"Excluir item de Checklist Triagem\", \"total\": \"Total\", \"parcial\": \"Parcial\", \"label-selecionar-arquivo\": \"Selecionar arquivo\", \"finalidade\": \"Finalidade\", \"detalhar\": \"Detalhar\", \"intervencao-obra\": \"Período de intervenção/Obra\", \"previsao-inicio\": \"Previsão de Início\", \"duracao-mes\": \"Duração em meses\", \"dados-autorizacao\": \"DADOS DA AUTORIZAÇÃO\", \"utilizacao-alterada\":\"A utilização atual foi alterada?\", \"sim\": \"Sim\", \"nao\": \"Não\", \"prazo-utilizacao\": \"Prazo de Utilição\", \"prazo-meses\": \"Prazo em Meses\", \"uso-principal\": \"Uso Principal\", \"especificacao-do-uso\": \"Especificação do Uso\", \"descricao-do-uso\": \"Descrição do Uso\", \"mensagem-campos-obrigatorios\": \"Necessário preencher os campos obrigatórios\", \"possuo-recurso\": \"Possuo recurso assegurado para execução da intervenção/obra (Será exigida documentação comprobatória)\", \"label-n-matricula-transcricao\": \"Nº da Matrícula/Transcrição\", \"label-n-processo\": \"Nº do Processo\", \"label-classificacao-imovel\": \"Classificação do Imóvel\", \"label-n-inscricao-municipal-rural-incra\": \"Nº de Inscrição [Municipal] [Rural/INCRA]\", \"label-prioridade-Atendimento\":\"Serviço com Prioridade no Atendimento\", \"msg-dados-brasil-cidadao\":\"Requerimento com dados preenchidos do Brasil Cidadão.\" }");
$templateCache.put("scripts-servicos/pages/breadcrumb/breadcrumb.html","<div id=breadcrumb><ul class=breadcrumb><span class=voce-esta-aqui>VOCÊ ESTÁ AQUI:</span><li class=migalha-pao ng-repeat=\"step in steps | limitTo:(steps.length-1)\"><a href={{step.ncyBreadcrumbLink}} ng-bind-html=step.ncyBreadcrumbLabel></a></li><li class=migalha-pao ng-repeat=\"step in steps | limitTo:-1\" class=active><span ng-bind-html=step.ncyBreadcrumbLabel></span></li></ul></div>");
$templateCache.put("scripts-servicos/app/directives/partials/justificativa.html","<md-dialog id=su-servicos aria-label=Suspender flex=60 ng-cloak><form name=adicionarSolicitantes><md-toolbar><div class=\"md-toolbar-tools text-center\"><h2>{{\'label-justificativa\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-margin><md-input-container class=md-block flex><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=justificativa required name=justificativa maxlength=500 md-maxlength=500></textarea></md-input-container></div><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=fechar() class=md-raised>Cancelar</md-button><md-button ng-click=confirmar() class=md-raised>Confirmar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/partials/modalCancelarRequerimento.html","<md-dialog ng-cloak flex=50><md-toast></md-toast><form><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-cancelar-requerimento\' | translate}}</h2><span flex></span><md-button ng-click=cancel()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content layout-margin=50><div layout=column style=\"margin-top: 3%\"><md-input-container><label>{{\'label-justificativa\' | translate}}</label><textarea required ng-model=justificativaCancelamento maxlength=800 md-maxlength=800 rows=5 md-select-on-focus flex></textarea></md-input-container><div layout=row layout-xs=column layout-align=\"end end\" layout-align-xs=\"center center\"><md-button ng-click=cancel()><label>{{\'fechar\' | translate}}</label></md-button><md-button ng-click=confirmarCancelamento($event)><label>{{\'btn-confirmar-cancelamento\' | translate}}</label></md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/partials/modalConfirmarCancelamento.html","<md-dialog ng-cloak><md-toast></md-toast><form><md-toolbar><div class=md-toolbar-tools><h2>{{\'cancelar-requerimento\' | translate}}</h2><span flex></span><md-button ng-click=cancel()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content layout-margin=50><div layout=column><label style=\"margin-top: 2%; margin-bottom: 2%\">{{\'msg-cancelamento\' | translate}}</label><div layout=row layout-align=\"end end\"><md-button ng-click=cancel()><label>{{\'fechar\' | translate}}</label></md-button><md-button ng-click=cancelarRequerimento()><label>{{\'confirmacao\' | translate}}</label></md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/partials/modalDocumentos.html","<md-dialog flex=50 aria-label=\"{{\'label-documentos\' | translate}}\" ng-cloak><form><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-documentos\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon class=fonte-branca aria-label=\"Close dialog\">close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div ng-if=detalhar><h3>{{\'label-arquivos-do-documento\' | translate}}: {{documento.nome}}</h3></div><md-container style=\"margin-top: 1%\" class=\"infoSocioEconomica table-responsive-vertical\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-nome-arquivo\' | translate | uppercase}}</th><th md-column class=texto-centro>{{\'label-acao\' | translate | uppercase}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"file in documento.files | orderBy: \'-name\':true\" ng-show=\"documento.files.length > 0\"><td md-cell><div ng-if=\"file &&file.idArquivo\"><div ng-if=!detalhar><a ng-href={{baixar(file.idArquivo)}}>{{ file.name }}</a></div><div ng-if=detalhar>{{ file.name }}</div></div></td><td md-cell><div ng-if=\"file &&!file.idArquivo\"><span class=cell-ellipsis>Erro ao anexar o arquivo solicite nova anexação</span></div><div ng-if=\"file && file.idArquivo\"><a ng-href={{baixar(file.idArquivo)}} ng-if=detalhar><ng-md-icon icon=description class=hand style=fill:#788489 size=18></ng-md-icon></a><ng-md-icon ng-show=\"!(naoRemoverAntigos && file.naoRemover)\" ng-if=!detalhar ng-click=\"removerArquivo($event, documento, file)\" icon=delete class=hand style=fill:#788489 size=18></ng-md-icon></div></td></tr><tr ng-show=\"!documento.files || documento.files.length == 0\"><td colspan=2>{{\'label-nenhum-arquivo-anexado\' | translate}}</td></tr></tbody></table></md-table-container></md-container></div></md-dialog-content><md-dialog-actions layout=row><md-button class=md-raised ng-click=fechar()>{{\'btn-fechar\' | translate}}</md-button></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/partials/modalDocumentosAnaliseSimp.html","<md-dialog flex=50 aria-label=\"{{\'label-documentos\' | translate}}\" ng-cloak><form><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-documentos\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=\"Close dialog\"></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div><h3>{{\'label-arquivos-do-documento\' | translate}}: {{documento.nome}}</h3></div><md-container style=\"margin-top: 1%\" class=\"infoSocioEconomica table-responsive-vertical\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-nome-arquivo\' | translate | uppercase}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate | uppercase}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"file in files | orderBy: \'-name\':true\"><td md-cell class=texto-centro-corpo>{{file.name}}</td><td md-cell class=texto-centro-corpo><a ng-href={{baixar(file.idArquivo)}}><ng-md-icon icon=description class=hand style=fill:#788489 size=18><md-tooltip>{{\'tooltip-download\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container></md-container></div></md-dialog-content><md-dialog-actions layout=row><md-button ng-click=fechar()>{{\'btn-fechar\' | translate}}</md-button></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/partials/modalFotosImovel.html","<md-dialog aria-label=UploadFotos flex=60 ng-cloak><form name=uploadFotosImovel><md-toolbar><div class=md-toolbar-tools><md-container layout=row><h2>{{\'incluir-fotos-imovel\' | translate}}</h2></md-container><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon aria-label=\"Fechar caixa de diálogo\">close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-margin><md-container class=md-block><label>{{\'fotos\' | translate}}*</label><input type=file id=file name=file multiple ngf-select=setFotosImovel($files) accept=image/* class=\"btn btn-warning\"></md-container></div><div layout=row><md-content style=\"width: 70% !important\" flex-offset=5><md-list ng-cloak><md-list-item ng-repeat=\"file in listaFotos\"><div class=row><a href=# ng-click=open($index)>{{file.name}}</a></div><div class=row><md-icon ng-click=removerFotoImovel($event,$index); aria-label=Fechar>close</md-icon></div></md-list-item></md-list></md-content></div><md-dialog-actions layout=row><md-button type=submit ng-click=confirmarUploadFotosImovel($event); style=margin-right:20px>{{\'confirmar\' | translate }}</md-button></md-dialog-actions></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/partials/modalOutrosDocumentos.html","<md-dialog flex=50 aria-label=\"{{\'label-outro-documentos\' | translate}}\" ng-cloak><form><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-outro-documentos\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon aria-label=\"Close dialog\">close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-xs=column><md-input-container><label>{{\'label-nome-documeto\' | translate}}</label><input ng-model=documento.nome></md-input-container><div flex=30><md-button ngf-multiple=true ngf-select=\"addDocumentos($files, documento)\">{{\'procurar\' | translate}}</md-button></div><md-list><md-list-item ng-repeat=\"file in documento.files\" style=\"min-height: 30%\"><p><a href=# ng-click=open(file)><label>{{file.name}}</label></a><md-icon ng-click=\"removerArquivo($event, documento, $index);\" class=\"color-red hand\">close</md-icon></p></md-list-item></md-list></div></div></md-dialog-content><md-dialog-actions layout=row><md-button ng-click=fechar()>{{\'btn-fechar\' | translate}}</md-button><md-button ng-click=salvar()>{{\'btn-salvar\' | translate}}</md-button></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-servicos/pages/analise-simplificada/language/pt-BR.json","{ }");
$templateCache.put("scripts-servicos/pages/analise-simplificada/views/analiseTecnicaSimplificada.html","<analise-simplificada id-requerimento=idRequerimento></analise-simplificada>");
$templateCache.put("scripts-servicos/pages/geral/view/template.html","<div flex layout=column ng-controller=\"MenuController as ul\"><!-- Barra de navageção --><cabecalho></cabecalho><!-- MENU --><menu></menu><!-- Conteúdo Principal --><md-content flex layout=column style=\"outline: none\"><div ui-view layout-padding style=\"outline: none\"></div></md-content></div><footer class=footer>Secretaria de Patrimônio da União - SPU | Esplanada dos Ministérios - Bloco C - 2º Andar Brasília-DF</footer>");
$templateCache.put("scripts-servicos/pages/grupo-processo-sei/language/pt-BR.json","{ \"toltip-limpar-campos\": \"Limpar campos\", \"toltip-pesquisar-grupos\": \"Pesquisar grupos de processo Sei\", \"toltip-adicionar-grupos\": \"Adicionar grupos de processo Sei\", \"label-uf\" : \"Selecione a UF\", \"label-sigla\" : \"Selecione a Sigla\", \"label-servico\": \"Serviço\", \"label-nome-titulo\": \"Nome/Título\", \"label-identidicador-portal-governo\": \"Identificador no Portal do Governo Federal\", \"label-permissoes-acessos\": \"Permissão Acessos\", \"label-link\": \"Link\", \"label-inserir-link\": \"Inserir Link\", \"label-formulario-web\": \"Formulários Web\", \"label-unidade-atendente\": \"Unidade Atendente (SEI)\", \"upload-formulario\": \"Upload de formulário em PDF\", \"label-alerta\": \"Alerta\", \"selecionar-arquivo\": \"Selecionar arquivo\", \"label-assuntos\": \"Assuntos\", \"label-palavras-chaves\": \"Palavras Chaves\", \"label-delete-button-palavra-chave\": \"Remover Palavra Chave\", \"delete-hint-palavra-chave\":\"Delete uma palavra chave\", \"secondary-placeholder-palavra-chave\": \"+Palavra Chave\", \"label-solicitantes\": \"Solicitantes\", \"label-segmentos\": \"Segmentos\", \"label-requisitos\": \"Requisitos\", \"label-nome-documento\": \"Nome do documento\", \"label-solicitante-obrigatorio\":\"Solicitante\", \"label-solicitante\":\"Solicitante\", \"label-segmento\": \"Segmento\", \"label-requisito-obrigatorio\": \"Requisitos\", \"label-tipo-obrigatorio\": \"Tipo\", \"label-documentos\": \"Documentos\", \"label-uf-unidade-atendimento\": \"De qual(is) Unidade(s) de Atendimento?\", \"label-uf-combo\" : \"Selecione as UFs\", \"label-email-complementar\": \"Email: \", \"label-combo-cep\": \"CEP\", \"label-checklist-analise-tecnica\": \"Checklist de Análise Técnica\", \"label-checklist-triagem\": \"Checklist de Triagem\", \"label-declaracoes\": \"Declarações\", \"label-item-checklist-validacao-requerimento\": \"Item do Checklist de Validação do Requerimento\", \"label-declaracao\": \"Declaração\", \"mensagem-arquivo-formato-invalido\" : \"Arquivo em formato inválido.\", \"label-exclusao\": \"Excluir Arquivo\", \"btn-adicionar-solicitante\": \"Adicionar Solicitante\", \"btn-adicionar-documento\": \"Adicionar Documento\", \"btn-adicionar-item-checklist\": \"Adicionar Item do Checklist\", \"btn-adicionar-declaracao\": \"Adicionar Declaração\", \"label-extensao\":\"Extensões:\", \"extensao\":\"Adicionar uma nova Extensão:\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-novo\": \"Novo\", \"btn-nova-unidade\":\"Nova Unidade Sei\", \"label-titulo-editar\": \"Editar Grupo Unidade Sei\", \"label-titulo-cadastrar\": \"Cadastrar Grupo Unidade Sei\", \"label-titulo-detalhar\": \"Detalhar Grupo Unidade Sei\", \"error-item-analise-tecnica\": \"É necessário informar o campo de Item da Análise Técnica.\", \"error-item-triagem\": \"É necessário informar o campo de Item da Triagem.\", \"error-min-caracteres\": \"É necessário informar no mínimo {{max}} caracteres\", \"abrir-iframe\": \"Deseja utilizar este link como iframe?\", \"error-informe-tipo\": \"É necessário informar o campo Tipo\", \"error-min-campo\": \"É necessário no mínimo {{min}} caracteres no campo de {{campo}}\", \"error-max-campo\": \"O campo de {{campo}} possui tamanho máximo de {{max}} caracteres\", \"error-informe-solicitante\": \"É necessário informar um Solicitante\", \"incluir-etapa-triagem\": \" Incluir etapa de triagem\", \"pressione-enter\": \" Pressione Enter para salvar a palavra\", \"selecione\": \"-- Selecione --\", \"checklist-analise_tecnica\": \"Checklist Análise Técnica\", \"checklist-triagem\": \"Checklist de Triagem\", \"marcacao-obrigatoria\": \"Marcação obrigatória\", \"mensagem-confirmar-exclusao\": \"Deseja confirmar exclusão?\", \"dados-complementares\": \"Dados Complementares\", \"arquivos-ajuda\": \"Arquivos de Ajuda\", \"arquivo-ajuda\": \"Arquivo de Ajuda\", \"tipo-campo\": \"Tipo do Campo\", \"combos\": \"Combos\", \"componentes\": \"Componentes\", \"nome-campo\": \"Nome do Campo\", \"nome-arquivo\": \"Nome do Arquivo\", \"valor-combo\": \"Valor do Combo\", \"upload-ajuda\": \"Upload de Arquivo de Ajuda\", \"obrigatoriedade\": \"Obrigatoriedade\", \"tamanho-campo\": \"Tamanho do campo\", \"mais-incluir-valor\": \" +Incluir Valor\", \"excluir-dados\": \"Excluir Dados Complementar\", \"editar-dados\": \"Editar Dados Complementar\", \"n-a\": \"N/A\", \"retirar-dados-imovel\": \"Retirar campos referentes ao imóvel\", \"servico-unidade-central\": \"Serviço da Unidade Central\", \"label-configuracao\": \"Configurações\", \"label-habilitar-Priorizacao-Atendimento\":\"Habilitar priorização de atendimento\", \"exigir-autenticacao-brasil-cidadao\":\"Exigir autenticação com Brasil Cidadão\", \"nivel-autenticacao-brasil-cidadao\":\"Qual nível de autenticação?\", \"conclusao-servico-triagem\": \"Habilitar conclusão do serviço na triagem\", \"conclusao-com-processo\": \"Com processo\", \"conclusao-sem-processo\": \"Sem processo\", \"subsecao\": \"Subseção\", \"adicionar-subsecao\": \"Adicionar Subseção\", \"nome-subsecao\": \"Nome da Subseção\", \"campo-subsecao\": \"Campo da Subseção\", \"campos-subsecao\": \"Campos da Subseção\" }");
$templateCache.put("scripts-servicos/pages/grupo-processo-sei/view/cadastrarGrupoProcessoSei.tmpl.html","<div class=standard><titulo-servico titulo-statico=titulo dinamico=true class=texto-principal></titulo-servico><form name=form ng-submit=\"validar() && salvar()\"><div layout-margin=5 layout=row layout-xs=column><md-input-container flex><label>{{\'label-nome-titulo\' | translate}}</label><input ng-model=grupo.descricao md-maxlength=250 maxlength=250 name=\"{{\'label-nome-titulo\' | translate}}\" ng-required=true></md-input-container></div><!-- botoes --><div layout=row layout-sm=column layout-align=\"end center\" flex=100><md-button class=md-raised type=submit>{{\'btn-gravar\' | translate | uppercase}}</md-button><md-button class=md-raised ng-click=novaUnidade(); exibir-elemento-por-permissao=SERVICOS.MANTER_UNIDADE_SEI ng-if=msgAlerta><md-tooltip md-direction=bottom>{{\'toltip-adicionar-unidades\' | translate}}</md-tooltip>{{\'btn-nova-unidade\' | translate }}</md-button><md-button class=md-raised ui-sref=servicos.SERVICOS_CONSULTAR_GRUPO_PROCESSO_SEI ng-if=\"grupo.id==null\">{{\'btn-retornar\' | translate | uppercase}}</md-button></div><div layout-margin=5 layout=row layout-sm=column layout-align=\"left center\"><label class=detalhar-label>{{msgAlerta}}</label></div></form><div flex style=\"margin-top: 3%\" id=su-unidades-grupo ng-if=grupo.id><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro>UF Unidade SEI</th><th md-column class=texto-centro>Sigla Unidade Sei</th><th md-column class=texto-centro>Código Unidade SEI</th><th md-column class=texto-centro style=\"text-align: center !important\">A&ccedil;&otilde;es</th></tr></thead><tbody md-body><tr md-row ng-if=unidadeSei><td md-cell class=texto-centro-corpo><md-input-container flex><md-select ng-model=unidadeSei.uf aria-label=UF ng-change=getSiglasUnidade()><md-option ng-value=0>{{\'label-uf\' | translate}}</md-option><md-option ng-repeat=\"uf in listaUfsDisponiveis\" value={{uf}}>{{uf}}</md-option></md-select></md-input-container></td><td md-cell class=texto-centro-corpo><md-input-container flex><md-select ng-model=unidadeSei.sigla aria-label=Sigla ng-change=setaCodigoUnidade() ng-if=listaSiglas><md-option ng-value=0>{{\'label-sigla\' | translate}}</md-option><md-option ng-repeat=\"sig in listaSiglas | orderBy\" value={{sig.sigla}}>{{sig.sigla}}</md-option></md-select></md-input-container></td><td md-cell class=texto-centro-corpo><span ng-bind=unidadeSei.codigoSei></span></td><td md-cell class=texto-centro-corpo><div layout=row layout-align=\"center center\"><div layout-margin=5><a><ng-md-icon icon=save style=fill:#788489 size=22 alt=\"Salvar unidade SEI\" ng-click=salvarUnidade();><md-tooltip>Salvar</md-tooltip></ng-md-icon></a></div><div layout-margin=5><a><ng-md-icon icon=cancel style=fill:#788489 size=22 alt=Cancelar ng-click=cancelar();><md-tooltip>Cancelar</md-tooltip></ng-md-icon></a></div></div></td></tr><tr md-row ng-repeat=\"item in listUnidades\" ng-if=!editandoUnidade><td md-cell class=texto-centro-corpo><span ng-bind=item.uf></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.sigla></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.codigoSei></span></td><td md-cell class=texto-centro-corpo><div layout=row layout-align=\"center center\"><div layout-margin=5><a><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"Editar unidade SEI\" ng-click=\"alterarUnidade($event, item);\"><md-tooltip>Editar unidade SEI</md-tooltip></ng-md-icon></a></div><div layout-margin=5><a href=\"\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"Excluir unidade SEI\" exibir-elemento-por-permissao=SERVICOS.MANTER_GRUPO_PROCESSO_SEI ng-click=\"showConfirmExcluir($event, item.idUnidade);\"><md-tooltip>Excluir unidade SEI</md-tooltip></ng-md-icon></a></div></div></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto; background-color: #5bf869\" ng-show=\"listUnidades.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findPagination md-page-select></md-table-pagination></md-content></div></div><div layout=row layout-sm=column layout-align=\"end center\" ng-if=grupo.id><md-button class=md-raised ui-sref=servicos.SERVICOS_CONSULTAR_GRUPO_PROCESSO_SEI>{{\'btn-retornar\' | translate | uppercase}}</md-button></div></div>");
$templateCache.put("scripts-servicos/pages/grupo-processo-sei/view/consultarGrupoProcessoSei.html","<titulo-servico class=texto-principal></titulo-servico><form name=dataTable><md-content flex><div layout=row><md-input-container flex><label>{{\'label-nome-titulo\' | translate}}</label><input ng-model=descricao></md-input-container><md-input-container flex><label>{{\'label-situacao\' | translate}}</label><md-select ng-model=situacao><md-option value=todos>Selecione</md-option><md-option value=ativo>Ativo</md-option><md-option value=inativo>Inativo</md-option></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=clearFilter()><md-tooltip md-direction=bottom>{{\'toltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate }}</md-button><md-button class=md-raised ng-click=filterFindAll() exibir-elemento-por-permissao=SERVICOS.MANTER_GRUPO_PROCESSO_SEI><md-tooltip md-direction=bottom>{{\'toltip-pesquisar-grupos\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate }}</md-button><md-button class=md-raised ng-click=novoCadastro(); exibir-elemento-por-permissao=SERVICOS.MANTER_GRUPO_PROCESSO_SEI><md-tooltip md-direction=bottom>{{\'toltip-adicionar-grupos\' | translate}}</md-tooltip>{{\'btn-novo\' | translate }}</md-button></section></div><!--- fim dos botoes --></md-content></form><div flex style=\"margin-top: 3%\" id=su-grupos><div layout=column ng-show=\"grupos.length > 0\"><md-content flex><md-table-container><table md-table ng-model=vm.grupolSelecionado><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro style=\"width: 565px\">Nome</th><th md-column class=texto-centro style=\"width: 565px\">Situação</th><th md-column class=texto-centro style=\"text-align: center !important\">A&ccedil;&otilde;es</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in grupos\"><td md-cell class=texto-centro-corpo><span ng-bind=item.descricao></span></td><td md-cell class=texto-centro-corpo><span ng-bind=\"item.situacao? \'Ativo\' : \'Inativo\'\"></span></td><td md-cell class=texto-centro-corpo><div layout=row layout-align=\"center center\"><div layout-margin=5><a href=#/servicos/cadastrargrupoprocessosei/{{item.id}}><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"Editar grupo de processo SEI\"><md-tooltip>Editar grupo de processo SEI</md-tooltip></ng-md-icon></a></div><div layout-margin=5><a style=\"text-decoration: none\" href=#/servicos/detalhargrupoprocessosei/{{item.id}} id=detalharGrupoProcessoSei><ng-md-icon class=hand icon=search style=fill:#788489 size=22 alt=\"\"><md-tooltip>Detalhar Grupo Processo SEI</md-tooltip></ng-md-icon></a></div><div layout-margin=5><a href=\"\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"Excluir grupo de processo SEI\" exibir-elemento-por-permissao=SERVICOS.MANTER_GRUPO_PROCESSO_SEI ng-click=\"showConfirmExcluir($event, item.id);\"><md-tooltip>Excluir grupo de processo SEI</md-tooltip></ng-md-icon></a></div></div></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto; background-color: #5bf869\" ng-show=\"grupos.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findPagination md-page-select></md-table-pagination></md-content></div></div>");
$templateCache.put("scripts-servicos/pages/grupo-processo-sei/view/detalharGrupoProcessoSei.html","<div class=standard><titulo-servico dinamico=!(isNew) titulo-statico=tituloStatico class=texto-principal></titulo-servico><div layout-margin=5 layout=row layout-xs=column><md-input-container flex><label>{{\'label-nome-titulo\' | translate}}</label><input ng-model=grupo.descricao disabled></md-input-container></div><div flex id=su-servicos-grupo><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro style=\"width: 565px\">Serviços</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listServicos\"><td md-cell class=texto-centro-corpo><span ng-bind=item.descricao></span></td></tr></tbody></table></md-table-container></md-content></div></div><div flex style=\"margin-top: 3%\" id=su-unidades-grupo><div layout=column><md-content flex><md-table-container><table md-table><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro style=\"width: 565px\">UF Unidade SEI</th><th md-column class=texto-centro style=\"width: 565px\">Sigla Unidade SEI</th><th md-column class=texto-centro style=\"width: 565px\">Código Unidade SEI</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in listUnidades\"><td md-cell class=texto-centro-corpo><span ng-bind=item.uf></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.sigla></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.codigoSei></span></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto; background-color: #5bf869\" ng-show=\"listUnidades.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findPagination md-page-select></md-table-pagination></md-content></div></div><!-- botoes --><div layout=row layout-sm=column layout-align=\"end center\"><md-button class=md-raised ui-sref=servicos.SERVICOS_CONSULTAR_GRUPO_PROCESSO_SEI>{{\'btn-retornar\' | translate | uppercase}}</md-button></div></div>");
$templateCache.put("scripts-servicos/pages/portal-redirecionamento/view/portalRedirecionamento.html","<titulo-servico class=texto-principal ng-if=hasValidUrl>Redirecionamento Do serviço</titulo-servico><div class=mensagem-informativa><p>O Atendimento de Requerimentos agora é realizado pelo Portal de Serviços da SPU</p><p class=link-destaque><a href=https://sistema.patrimoniodetodos.gov.br/ target=_blank class=link-portal>🔗 https://sistema.patrimoniodetodos.gov.br/</a></p><p>Ao acessar o portal, faça login com sua conta <b>gov.br</b>, clicando no canto superior direito da página.</p><p>Em seguida, acesse o menu: Área de Atendimento > SERVIÇOS (PORTAL SPU/MP) > Serviços > Requerimentos</p><p>Em caso de dúvidas, entre em contato pelo canal: <a href=mailto:cgate@gestao.gov.br>cgate@gestao.gov.br</a></p></div>");
$templateCache.put("scripts-servicos/pages/requerimento-autorizacao-obra/language/pt-BR.json","{ \"Dados do Evento\":\"\", \"dados-evento\":\"DADOS EVENTO\", \"nome-evento\":\"Nome do Evento\", \"descricao-evento\":\"Descrição do Evento\", \"natureza\":\"Natureza\", \"informacao-economica-comercial\":\"Informações Ecônomica/Comercial\", \"periodo-utilizacao-area\":\"Periodo de Utilização da Área\", \"data-de-realizacao-evento\":\"Data de Realização do Evento\", \"data-inicial\":\"Data Inicial\", \"data-final\":\"Data Final\", \"prazo\":\"Prazo (dias)\", \"tipo-minusculo\":\"tipo\", \"Dados da Area do Evento \":\"\", \"dados-area-evento\":\"DADOS DA ÁREA DO EVENTO\", \"endereco-referencia\": \"Endereço de Referência\", \"area-abrangencia\":\"Área de abrangência do evento(Desenhe no mapa abaixo)\", \"area-exploracao-info\":\"A área de exploração economica corresponde a área total do evento\", \"area-exploracao-campo\":\"Área de exploração economica: \", \"planta\":\"Planta\", \"documentos\": \"DOCUMENTOS\", \"Dados do Documentos\":\"\", \"dados-documentos\":\"Documentos\", \"complementares\":\"Complementares\", \"obrigatorios\":\"Obrigatórios\", \"possuo\":\"Possuo\", \"nao-possuo\":\"Não Possuo\", \"nao-possuo-consegui\":\"Não possuo mas posso conseguir\", \"Informacao Socioeconomica\":\"\", \"Dados Modelo Placa\":\"\", \"modelo-placa\":\"MODELO PLACA FAIXA\", \"modelo-placa-pdf\":\"Modelo de Placa e Faixa\", \"Dados Correspondecia\":\"\", \"dados-correspondencia\":\"ENDEREÇO DE CORRESPONDÊNCIA\", \"Dados do Imvoel\":\"\", \"Dados do Regime\":\"\", \"dados-regime\":\"DADOS DO REGIME/CONTRATO\", \"regime-contrato\":\"Regime/Contrato Pretendido:\", \"uso-pretendido\":\"Uso Pretendido:\", \"descricao-uso\":\"Descrição do uso:\", \"Dados do Aquisicao\":\"\", \"dados-aquisicao\":\"FORMA DE AQUISICAO\", \"Termos\":\"\", \"declaro-conhecimento\":\"Declaro ter conhecimento que o presente requerimento fundamenta-se no disposto no art.22 da Lei 9636/1988 e no art.14 e seus parágrafos do Decreto 3725/2001.\", \"declaro-veracidade\":\"Declaro, sob pena da lei, que as informações prestadas e os documentos apresentados são verdadeiros.\", \"Botoes\":\"\", \"cancelar\":\"Cancelar\", \"salvar\":\"Salvar\", \"selecionar-arquivo\": \"Selecionar Arquivo\", \"selecionar-arquivos\": \"Selecionar Arquivos\", \"procurar\":\"Procurar\", \"adicionar\":\"Adicionar\", \"enviar\":\"Enviar\", \"confirmar\": \"Confirmar\", \"fechar\": \"Fechar\", \"acao\":\"Ação\", \"Comum\":\"\", \"sim\":\"Sim\", \"nao\":\"Não\", \"texto-falecido\":\"Atenção: O envio da Certidão de Óbito do Requerente é obrigatório para a análise desse requerimento\", \"selecione\":\"--Selecione--\", \"label-nome-rendimento-membro-familia\": \"Relacione o nome e rendimento do(s) membro(s) da família que contribui(em) para a renda familiar.\", \"label-dependentes-economicos\": \"Possui dependentes econômicos:\", \"label-renda-familiar-mensal\": \"Renda familiar mensal (R$):\", \"label-profissao\": \"Profissão:\", \"label-adicionar\": \"Adicionar\", \"label-excluir\": \"Excluir\", \"label-renda-mensal-tipo-modeda\": \"Renda Mensal (R$)\", \"label-alerta\": \"Alerta\", \"label-confirmacao\": \"Confirmação\", \"label-protocolo-atendimento\": \"Prezado usuário, sua solicitação foi recebida com sucesso. O número do seu Atendimento é {{parametro}}\", \"label-cpf\": \"CPF\", \"label-renda-mensal\": \"Renda Mensal\", \"label-acao\": \"ação\", \"label-pendencias\": \"Pendências\", \"label-analise-requerimento-realizada\": \"Análise do requerimento realizada em \", \"label-itens-nao-atendidos\": \"Itens não atendidos\", \"label-analise\": \"Análise\", \"label-justificativa-anotacao\": \"Justificativa/Anotação\", \"label-detalhamento-pendencia\": \"Detalhamento da Pendência\", \"label-avaliacao-dia\": \"Avaliação do dia \", \"mensagem-recaptcha\":\"É obrigatório o preenchimento do capcha.\", \"mensagem-confirmacao-email\": \"E-mails de contato não conferem\", \"mensagem-ctrl-bloqueado\": \"Não é permitido CTRL + C\", \"mensagem-dependente-existente\": \"Dependente já informado\", \"mensagem-confirmar-exclusao\": \"Deseja confirmar exclusão?\", \"mensagem-dados-invalidos\": \"Dados invalidos\", \"mensagem-um-telefone-obrigatorio\": \"É obrigatório informar um telefone.\", \"mensagem-um-telefone-obrigatorio-requerente\": \"Informar um Telefone nos Dados do Requerente\", \"mensagem-cep-cartorio-diferente-imovel\": \"O Cartório e Imóvel devem estar na mesma Unidade Federativa\", \"mensagem-tamanho-max-arquivo\": \"O arquivo não pode possuir mais de 5mb\", \"mensagem-arquivo-formato-invalido\": \"Arquivo em formato inválido.\", \"mensagem-documentos-obrigatorios\": \"Os documentos obrigatórios devem ser informados.\", \"msg-min-caracteres\": \"É obrigatório informar no minimo 10 caracteres no campo {{parametro}}\", \"msg-cancelamento\":\"Após o cancelamento, este requerimento não será mais analisado. Deseja continuar?\", \"objetivo-requerimento\": \"Objetivo do Requerimento\", \"descricao-objetivo\": \"Descrição do Objetivo\", \"cartorio-registro-imoveis\": \"O requerente é cartório de registro de imóveis?\", \"selecionar-cartorio\": \"Selecionar Cartório\", \"nome-cartorio\": \"Nome do Cartório\", \"selecao-cartorio\": \"Seleção do cartório\", \"campo-obrigatorio\": \"Campo de preenchimento obrigatório\", \"selecione-normal\": \"Selecione\", \"btn-limpar\": \"Limpar\", \"btn-fechar\": \"Fechar\", \"btn-visualizar-requerimento\": \"Visualizar Requerimento\", \"escolher-localizacao-imovel\": \"Escolha uma das opções abaixo para indicar a localização do imóvel\", \"marcar-ponto-mapa\": \"Marcar ponto no mapa\", \"desenhar-poligonal\": \"Desenhar poligonal do terreno no mapa\", \"anexar-geometria\": \"Anexar geometria do terreno\", \"arquivo-formato-shapefile\": \"Arquivos em formato shapefile da poligonal do terreno\", \"informar-coordenada\": \"Informar coordenadas geográfcas do acesso principal do imóvel\", \"latitude\": \"Latitude\", \"longitude\": \"Longitude\", \"fonte\": \"Fonte\", \"google-earth\": \"Google Earth\", \"google-maps\": \"Google Maps\", \"incluir-planta\": \"Incluir Planta\", \"posicao\": \"Posição\", \"descricao\": \"Descrição\", \"tipo-imovel\": \"Tipo do Imóvel\", \"possui-area-construida\": \"Possui área construída?\", \"area-construida\": \"Área Construída(m²)\", \"fotos-imovel\": \"Fotos do Imóvel (Externas do imóvel e das benfeitorias se existentes)\", \"adicionar-foto\": \"Adicionar Foto\", \"incluir-fotos-imovel\": \"Incluir Fotos Imóvel\", \"fotos\": \"Fotos\", \"maximo-foto\": \"São permitidas, no máximo, 4 fotos.\", \"formato-invalido\": \"Formato de arquivo inválido\", \"incluir-duas-fotos\": \"Incluir pelo menos 2 fotos\", \"relacione\": \"Relacione o nome e rendimento do(s) membro(s) da família que contribui(em) para a renda familiar.\", \"renda-maiusculo\": \"RENDA MENSAL\", \"tipo\": \"Tipo\", \"acao\": \"Ação\", \"anotacao\":\"Anotações\", \"iniciar-analise\":\"Iniciar Ánalise\", \"cancelar-requerimento\":\"Cancelar Requerimento\", \"confirmar-cancelamento\":\"Confirmar Cancelamento\" }");
$templateCache.put("scripts-servicos/pages/requerimento-autorizacao-obra/view/detalharRequerimentoAutorizacaoObra.html","<barra-portal></barra-portal><div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><div><fieldset class=standard><legend><h3>{{\'dados-correspondencia\' | translate}}</h3></legend><detalhar-endereco-correspondencia requerimento=requerimento></detalhar-endereco-correspondencia></fieldset></div><span class=\"texto-verde detalhar-label\" ng-show=exibirRepresentanteLegal(requerimento);>{{\'dados-representante-legal\' | translate}}</span><div ng-show=exibirRepresentanteLegal(requerimento);><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span class=\"texto-verde detalha-label\">{{\'dados-autorizacao\' | translate}}</span><div><detalhar-autorizacao requerimento=requerimento></detalhar-autorizacao></div><span class=\"texto-verde detalhar-label\">{{\"dados-imovel\" | translate | uppercase}}</span><div><detalhar-imovel requerimento=requerimento tipo=\"\'AUTORIZACAO_OBRAS\'\"></detalhar-imovel></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><div flex=100 class=md-block style=\"width: 100%\"><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/requerimento-cancelamento-cobranca/language/pt-BR.json","{ \"dados-cancelamento\": \"Dados do Cancelamento\", \"label-motivo-pedido\": \"Motivo do Pedido\", \"label-num-processo-atendimento-requerimento\": \"Nº do Processo ou Atendimento de Requerimento de [Isenção][Transferência]\", \"label-descricao\": \"Descrição\", \"label-cancelar-todos-debitos\": \"Cancelar todos os débitos\", \"label-selecionar-exercicios\": \"Selecione um ou mais exercícios\", \"label-marcar-todos-debitos\": \"Marcar todos os débitos\", \"label-anos-exercicios\": \"Anos/Exercícios\", \"label-cancelar-todos-debitos\": \"Cancelar todos os débitos\" }");
$templateCache.put("scripts-servicos/pages/requerimento-cancelamento-cobranca/views/detalharRequerimentoCancelamentoCobranca.html","<barra-portal></barra-portal><div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><!--    <div layout=\"row\">\n            <informacao-servico flex=\"100\" ng-model=\"servico\" id-servico=\"idServico\"></informacao-servico>\n        </div>--><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><span class=\"texto-verde detalhar-label\">{{\'dados-correspondencia\' | translate}}</span><div><detalhar-endereco-correspondencia requerimento=requerimento></detalhar-endereco-correspondencia></div><span class=\"texto-verde detalhar-label\" ng-show=exibirRepresentanteLegal(requerimento);>{{\'dados-representante-legal\' | translate}}</span><div ng-show=exibirRepresentanteLegal(requerimento);><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\">{{\'dados-cancelamento-cobranca\' | translate}}</span><div><detalhar-cancelamento-cobranca requerimento=requerimento></detalhar-cancelamento-cobranca></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><div flex=100 class=md-block style=\"width: 100%\"><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/requerimento-cancelamento-utilizacao/language/pt-BR.json","{ \"dados-utilizacao-cancelamento\": \"DADOS DA UTILIZAÇÃO/CANCELAMENTO\" }");
$templateCache.put("scripts-servicos/pages/requerimento-cancelamento-utilizacao/views/detalharRequerimentoCancelamentoUtilizacao.html","<barra-portal></barra-portal><div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><span class=\"texto-verde detalhar-label\" ng-show=exibirRepresentanteLegal(requerimento);>{{\'dados-representante-legal\' | translate}}</span><div ng-show=exibirRepresentanteLegal(requerimento);><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\">{{\'dados-utilizacao-cancelamento\' | translate}}</span><div><detalhar-cancelamento-utilizacao requerimento=requerimento></detalhar-cancelamento-utilizacao></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><div flex=100 class=md-block style=\"width: 100%\"><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/requerimento-consulta-dominialidade/language/pt-BR.json","{ \"label-nome-cartorio\": \"Nome do Cartório\" }");
$templateCache.put("scripts-servicos/pages/requerimento-isencao/language/pt-BR.json","{ \"Dados do Requerente\":\"\", \"dados-requerente\":\"DADOS DO REQUERENTE\", \"cpf-cnpj\":\"CPF/CNPJ:\", \"nome-razao-social\":\"Nome/Razão Social:\", \"tipo\":\"Tipo:\", \"cep\":\"CEP:\", \"rip\": \"RIP\", \"nao-sabe-cep\":\"Não sabe seu CEP?\", \"uf\":\"UF:\", \"municipio\":\"Município:\", \"tipo-logradouro\":\"Tipo Logradouro:\", \"logradouro\":\"Logradouro:\", \"numero\":\"Número:\", \"bairro\":\"Bairro:\", \"complemento\":\"Complemento:\", \"email\":\"E-mail:\", \"repetir-email\":\"Repetir E-mail:\", \"telefone-fixo\":\"Telefone Fixo:\", \"telefone-celular\":\"Telefone Celular:\", \"telefone-comercial\":\"Telefone Comercial:\", \"o-que-voce-esta-procurando\":\"O que você está procurando\", \"falecido\":\"Falecido:\", \"endereco-correspondencia\":\"Utilizar este endereço para correspondência:\", \"Dados do Representante Legal\":\"\", \"dados-representante-legal\":\"DADOS DO REPRESENTANTE LEGAL\", \"cpf\":\"CPF:\", \"nome\":\"Nome:\", \"cargo\":\"Cargo:\", \"Dados do Evento\":\"\", \"dados-evento\":\"DADOS EVENTO\", \"nome-evento\":\"Nome do Evento\", \"descricao-evento\":\"Descrição do Evento\", \"natureza\":\"Natureza\", \"informacao-economica-comercial\":\"Informações Ecônomica/Comercial\", \"periodo-utilizacao-area\":\"Periodo de Utilização da Área\", \"data-de-realizacao-evento\":\"Data de Realização do Evento\", \"data-inicial\":\"Data Inicial\", \"data-final\":\"Data Final\", \"prazo\":\"Prazo (dias)\", \"tipo-minusculo\":\"tipo\", \"Dados da Area do Evento \":\"\", \"dados-area-evento\":\"DADOS DA ÁREA DO EVENTO\", \"endereco-referencia\": \"Endereço de Referência\", \"area-abrangencia\":\"Área de abrangência do evento(Desenhe no mapa abaixo)\", \"area-exploracao-info\":\"A área de exploração economica corresponde a área total do evento\", \"area-exploracao-campo\":\"Área de exploração economica: \", \"planta\":\"Planta\", \"documentos\": \"DOCUMENTOS\", \"Dados do Documentos\":\"\", \"dados-documentos\":\"Documentos\", \"complementares\":\"Complementares\", \"obrigatorios\":\"Obrigatórios\", \"possuo\":\"Possuo\", \"nao-possuo\":\"Não Possuo\", \"nao-possuo-consegui\":\"Não possuo mas posso conseguir\", \"Informacao Socioeconomica\":\"\", \"dados-socioecomica\":\"INFORMAÇÕES SOCIOECONÔMICAS\", \"label-profissao\":\"Profissão\", \"renda-familiar\":\"Renda Familiar Mensal (R$)\", \"possui-dependentes\":\"Possui dependentes economicos\", \"quantos\":\"Quantos\", \"renda\":\"Renda Mensal (R$)\", \"Dados Modelo Placa\":\"\", \"modelo-placa\":\"MODELO PLACA FAIXA\", \"modelo-placa-pdf\":\"Modelo de Placa e Faixa\", \"Dados Correspondecia\":\"\", \"dados-correspondencia\":\"ENDEREÇO DE CORRESPONDÊNCIA\", \"Dados do Imvoel\":\"\", \"dados-imovel\":\"DADOS DO IMOVEL\", \"area-terreno\":\"Área do Terreno(m²)\", \"area-contruida\":\"Área Construída(m²)\", \"Dados do Regime\":\"\", \"dados-regime\":\"DADOS DO REGIME/CONTRATO\", \"regime-contrato\":\"Regime/Contrato Pretendido:\", \"uso-pretendido\":\"Uso Pretendido:\", \"descricao-uso\":\"Descrição do uso:\", \"Dados do Aquisicao\":\"\", \"dados-aquisicao\":\"FORMA DE AQUISICAO\", \"Termos\":\"\", \"declaro-conhecimento\":\"Declaro ter conhecimento que o presente requerimento fundamenta-se no disposto no art.22 da Lei 9636/1988 e no art.14 e seus parágrafos do Decreto 3725/2001.\", \"declaro-veracidade\":\"Declaro, sob pena da lei, que as informações prestadas e os documentos apresentados são verdadeiros.\", \"Botoes\":\"\", \"cancelar\":\"Cancelar\", \"salvar\":\"Salvar\", \"selecionar-arquivo\": \"Selecionar Arquivo\", \"selecionar-arquivos\": \"Selecionar Arquivos\", \"procurar\":\"Procurar\", \"adicionar\":\"Adicionar\", \"enviar\":\"Enviar\", \"confirmar\": \"Confirmar\", \"fechar\": \"Fechar\", \"acao\":\"Ação\", \"Comum\":\"\", \"sim\":\"Sim\", \"nao\":\"Não\", \"texto-falecido\":\"Atenção: O envio da Certidão de Óbito do Requerente é obrigatório para a análise desse requerimento\", \"selecione\":\"--Selecione--\", \"label-nome-rendimento-membro-familia\": \"Relacione o nome e rendimento do(s) membro(s) da família que contribui(em) para a renda familiar.\", \"label-dependentes-economicos\": \"Possui dependentes econômicos:\", \"label-renda-familiar-mensal\": \"Renda familiar mensal (R$):\", \"label-profissao\": \"Profissão:\", \"label-adicionar\": \"Adicionar\", \"label-excluir\": \"Excluir\", \"label-renda-mensal-tipo-modeda\": \"Renda Mensal (R$)\", \"label-alerta\": \"Alerta\", \"label-confirmacao\": \"Confirmação\", \"label-protocolo-atendimento\": \"Prezado usuário, sua solicitação foi recebida com sucesso. O número do seu Atendimento é {{parametro}}\", \"label-cpf\": \"CPF\", \"label-renda-mensal\": \"Renda Mensal\", \"label-acao\": \"ação\", \"label-pendencias\": \"Pendências\", \"label-analise-requerimento-realizada\": \"Análise do requerimento realizada em \", \"label-itens-nao-atendidos\": \"Itens não atendidos\", \"label-analise\": \"Análise\", \"label-justificativa-anotacao\": \"Justificativa/Anotação\", \"label-detalhamento-pendencia\": \"Detalhamento da Pendência\", \"label-avaliacao-dia\": \"Avaliação do dia \", \"mensagem-recaptcha\":\"É obrigatório o preenchimento do capcha.\", \"mensagem-confirmacao-email\": \"E-mails de contato não conferem\", \"mensagem-ctrl-bloqueado\": \"Não é permitido CTRL + C\", \"mensagem-dependente-existente\": \"Dependente já informado\", \"mensagem-confirmar-exclusao\": \"Deseja confirmar exclusão?\", \"mensagem-dados-invalidos\": \"Dados invalidos\", \"mensagem-um-telefone-obrigatorio\": \"É obrigatório informar um telefone.\", \"mensagem-um-telefone-obrigatorio-requerente\": \"Informar um Telefone nos Dados do Requerente\", \"mensagem-cep-cartorio-diferente-imovel\": \"O Cartório e Imóvel devem estar na mesma Unidade Federativa\", \"mensagem-tamanho-max-arquivo\": \"O arquivo não pode possuir mais de 5mb\", \"mensagem-arquivo-formato-invalido\": \"Arquivo em formato inválido.\", \"mensagem-documentos-obrigatorios\": \"Os documentos obrigatórios devem ser informados.\", \"mensagem-campos-obrigatorios\": \"Necessário preencher os campos obrigatórios\", \"msg-min-caracteres\": \"É obrigatório informar no minimo 10 caracteres no campo {{parametro}}\", \"msg-cancelamento\":\"Após o cancelamento, este requerimento não será mais analisado. Deseja continuar?\", \"objetivo-requerimento\": \"Objetivo do Requerimento\", \"descricao-objetivo\": \"Descrição do Objetivo\", \"cartorio-registro-imoveis\": \"O requerente é cartório de registro de imóveis?\", \"selecionar-cartorio\": \"Selecionar Cartório\", \"nome-cartorio\": \"Nome do Cartório\", \"selecao-cartorio\": \"Seleção do cartório\", \"campo-obrigatorio\": \"Campo de preenchimento obrigatório\", \"selecione-normal\": \"Selecione\", \"btn-limpar\": \"Limpar\", \"btn-fechar\": \"Fechar\", \"btn-visualizar-requerimento\": \"Visualizar Requerimento\", \"escolher-localizacao-imovel\": \"Escolha uma das opções abaixo para indicar a localização do imóvel\", \"marcar-ponto-mapa\": \"Marcar ponto no mapa\", \"desenhar-poligonal\": \"Desenhar poligonal do terreno no mapa\", \"anexar-geometria\": \"Anexar geometria do terreno\", \"arquivo-formato-shapefile\": \"Arquivos em formato shapefile da poligonal do terreno\", \"informar-coordenada\": \"Informar coordenadas geográfcas do acesso principal do imóvel\", \"latitude\": \"Latitude\", \"longitude\": \"Longitude\", \"fonte\": \"Fonte\", \"google-earth\": \"Google Earth\", \"google-maps\": \"Google Maps\", \"incluir-planta\": \"Incluir Planta\", \"posicao\": \"Posição\", \"descricao\": \"Descrição\", \"tipo-imovel\": \"Tipo do Imóvel\", \"possui-area-construida\": \"Possui área construída?\", \"area-construida\": \"Área Construída(m²)\", \"fotos-imovel\": \"Fotos do Imóvel (Externas do imóvel e das benfeitorias se existentes)\", \"adicionar-foto\": \"Adicionar Foto\", \"incluir-fotos-imovel\": \"Incluir Fotos Imóvel\", \"fotos\": \"Fotos\", \"maximo-foto\": \"São permitidas, no máximo, 4 fotos.\", \"formato-invalido\": \"Formato de arquivo inválido\", \"incluir-duas-fotos\": \"Incluir pelo menos 2 fotos\", \"relacione\": \"Relacione o nome e rendimento do(s) membro(s) da família que contribui(em) para a renda familiar.\", \"renda-maiusculo\": \"RENDA MENSAL\", \"tipo\": \"Tipo\", \"acao\": \"Ação\", \"anotacao\":\"Anotações\", \"iniciar-analise\":\"Iniciar Ánalise\", \"cancelar-requerimento\":\"Cancelar Requerimento\", \"confirmar-cancelamento\":\"Confirmar Cancelamento\" }");
$templateCache.put("scripts-servicos/pages/requerimento-isencao/view/detalharRequerimentoIsencao.html","<div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><div><fieldset class=standard><legend><h3>{{\'dados-correspondencia\' | translate}}</h3></legend><detalhar-endereco-correspondencia requerimento=requerimento></detalhar-endereco-correspondencia></fieldset></div><span class=\"texto-verde detalhar-label\" ng-if=verificarReqPossuiInfoSocioEconomicas()>{{\'dados-socioecomica\' | translate}}</span><div ng-if=verificarReqPossuiInfoSocioEconomicas()><detalhar-informacoes-socio-economicas requerimento=requerimento></detalhar-informacoes-socio-economicas></div><span class=\"texto-verde detalhar-label\">{{\'dados-imovel\' | translate}}</span><div><detalhar-imovel requerimento=requerimento></detalhar-imovel></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><div flex=100 class=md-block style=\"width: 100%\" ng-show=requerimento.anotacoes><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/requerimento-mudanca-regime-contrato/language/pt-BR.json","{ }");
$templateCache.put("scripts-servicos/pages/requerimento-mudanca-regime-contrato/views/detalharRequerimentoMudancaRegimeContrato.html","<div><detalhar-titulo requerimento=detalharMudancaRegimeContratoCtrl.requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=detalharMudancaRegimeContratoCtrl.requerimento></detalhar-requerente><h2 class=texto-verde>{{\'dados-correspondencia\' | translate}}</h2><detalhar-endereco-correspondencia requerimento=detalharMudancaRegimeContratoCtrl.requerimento></detalhar-endereco-correspondencia></div><div><h2 class=texto-verde>{{\'dados-imovel\' | translate}}</h2><detalhar-imovel requerimento=detalharMudancaRegimeContratoCtrl.requerimento></detalhar-imovel></div><div><detalhar-dados-regime-contrato requerimento=detalharMudancaRegimeContratoCtrl.requerimento></detalhar-dados-regime-contrato></div><div ng-if=detalharMudancaRegimeContratoCtrl.verificarReqPossuiInfoSocioEconomicas()><h2 class=texto-verde>{{\'dados-socioecomica\' | translate}}</h2><detalhar-informacoes-socio-economicas requerimento=detalharMudancaRegimeContratoCtrl.requerimento></detalhar-informacoes-socio-economicas></div><div><h2 class=texto-verde>{{\'dados-documentos\' | translate}}</h2><detalhar-documento flex=100 id-servico=detalharMudancaRegimeContratoCtrl.requerimento.servico.id id-tipo-solicitante=detalharMudancaRegimeContratoCtrl.requerimento.requerente.tipoSolicitante.id tipo-solicitante=detalharMudancaRegimeContratoCtrl.requerimento.requerente.tipoSolicitante.tipo id-requerimento=detalharMudancaRegimeContratoCtrl.requerimento.id></detalhar-documento></div><div layout=row ng-if=detalharMudancaRegimeContratoCtrl.requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=detalharMudancaRegimeContratoCtrl.requerimento></detalhar-prioridade-atendimento></div><div flex=100 class=md-block style=\"width: 100%\" ng-show=\"detalharMudancaRegimeContratoCtrl.requerimento.anotacoes.length > 0\"><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=detalharMudancaRegimeContratoCtrl.requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=detalharMudancaRegimeContratoCtrl.requerimento></acoes-requerimento-servico></div></div>");
$templateCache.put("scripts-servicos/pages/requerimento-nova-consulta-dominialidade/language/pt-BR.json","{ \"label-tipo-requerente\": \"Tipo de Requerente\", \"label-nome-cartorio\": \"Nome do Cartório\", \"label-ug-unidade\": \"UG/Unidade\" }");
$templateCache.put("scripts-servicos/pages/requerimento-regularizacao-uso-imovel/language/pt-BR.json","{ \"endereco-requerenet-igual-imovel\": \"O endereço do Requerente é o mesmo do Imóvel?\", \"complemento-imovel\": \"Complemento(Ex: nª apartamento, nome do prédio)\", \"indique-localizacao-mapa\": \"Indique no mapa a localização do imóvel para facilitar a análise do requerimento.\", \"usar-minha-localizacao\": \"Usar minha localização.\", \"add-foto\": \"Adicionar Foto\", \"uso-principal\": \"Uso principal\", \"especificacao-uso\": \"Especificação do Uso\", \"descricao-uso\": \"Descrição do Uso\", \"data-ocupacao\": \"Data de início da ocupação\", \"numero-familias\": \"Número de familías\", \"nome-rendimento-dependentes\": \"Relacione o nome e rendimento do(s) membro(s) da família que contribui(em) para a renda familiar.\", \"renda-mensal-uppercase\": \"RENDA MENSAL\", \"enviar-uppercase\": \"ENVIAR\", \"cancelar-uppercase\": \"CANCELAR\", \"possui-area-construida\": \"Possui área construída?\", \"area-construida\": \"Área Construída(m²)\", \"fotos-imovel\": \"Fotos do Imóvel (Externas do imóvel e das benfeitorias se existentes)\" }");
$templateCache.put("scripts-servicos/pages/requerimento-regularizacao-uso-imovel/views/detalharRequerimentoRegularizacaoUso.html","<barra-portal></barra-portal><div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><span class=\"texto-verde detalhar-label\" ng-show=exibirRepresentanteLegal(requerimento);>{{\'dados-representante-legal\' | translate}}</span><div ng-show=exibirRepresentanteLegal(requerimento);><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\">{{\'dados-imovel\' | translate}}</span><div><detalhar-imovel-uniao requerimento=requerimento preview-fotos=requerimento.imovel.fotosImovel></detalhar-imovel-uniao></div><span class=\"texto-verde detalhar-label\" ng-if=verificarReqPossuiInfoSocioEconomicas()>{{\'dados-socioecomica\' | translate}}</span><div ng-if=verificarReqPossuiInfoSocioEconomicas()><detalhar-informacoes-socio-economicas requerimento=requerimento></detalhar-informacoes-socio-economicas></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><div flex=100 class=md-block style=\"width: 100%\"><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/requerimento-remicao-aforamento/language/pt-BR.json","{ \"endereco-requerenet-igual-imovel\": \"O endereço do Requerente é o mesmo do Imóvel?\", \"complemento-imovel\": \"Complemento(Ex: nª apartamento, nome do prédio)\", \"indique-localizacao-mapa\": \"Indique no mapa a localização do imóvel para facilitar a análise do requerimento.\", \"usar-minha-localizacao\": \"Usar minha localização.\", \"add-foto\": \"Adicionar Foto\", \"uso-principal\": \"Uso principal\", \"especificacao-uso\": \"Especificação do Uso\", \"descricao-uso\": \"Descrição do Uso\", \"data-ocupacao\": \"Data de início da ocupação\", \"numero-familias\": \"Número de familías\", \"nome-rendimento-dependentes\": \"Relacione o nome e rendimento do(s) membro(s) da família que contribui(em) para a renda familiar.\", \"renda-mensal-uppercase\": \"RENDA MENSAL\", \"enviar-uppercase\": \"ENVIAR\", \"cancelar-uppercase\": \"CANCELAR\", \"possui-area-construida\": \"Possui área construída?\", \"area-construida\": \"Área Construída(m²)\", \"fotos-imovel\": \"Fotos do Imóvel (Externas do imóvel e das benfeitorias se existentes)\" }");
$templateCache.put("scripts-servicos/pages/requerimento-remicao-aforamento/views/detalharRequerimentoRemicaoAforamento.html","<barra-portal></barra-portal><div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><span class=\"texto-verde detalhar-label\" ng-show=\"requerimento.requerente.representanteLegal != undefined\">{{\'dados-representante-legal\' | translate}}</span><div ng-show=\"requerimento.requerente.representanteLegal != undefined\"><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\">{{\'dados-imovel\' | translate}}</span><div><detalhar-imovel-uniao requerimento=requerimento preview-fotos=requerimento.imovel.fotosImovel></detalhar-imovel-uniao></div><span class=\"texto-verde detalhar-label\">{{\'dados-formaAquisicao\' | translate}}</span><div><detalhar-forma-aquisicao requerimento=requerimento></detalhar-forma-aquisicao></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><div flex=100 class=md-block style=\"width: 100%\"><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/requerimento-revigoracao-aforamento/language/pt-BR.json","{ \"endereco-requerenet-igual-imovel\": \"O endereço do Requerente é o mesmo do Imóvel?\", \"complemento-imovel\": \"Complemento(Ex: nª apartamento, nome do prédio)\", \"indique-localizacao-mapa\": \"Indique no mapa a localização do imóvel para facilitar a análise do requerimento.\", \"usar-minha-localizacao\": \"Usar minha localização.\", \"add-foto\": \"Adicionar Foto\", \"uso-principal\": \"Uso principal\", \"especificacao-uso\": \"Especificação do Uso\", \"descricao-uso\": \"Descrição do Uso\", \"data-ocupacao\": \"Data de início da ocupação\", \"numero-familias\": \"Número de familías\", \"nome-rendimento-dependentes\": \"Relacione o nome e rendimento do(s) membro(s) da família que contribui(em) para a renda familiar.\", \"renda-mensal-uppercase\": \"RENDA MENSAL\", \"enviar-uppercase\": \"ENVIAR\", \"cancelar-uppercase\": \"CANCELAR\", \"possui-area-construida\": \"Possui área construída?\", \"area-construida\": \"Área Construída(m²)\", \"fotos-imovel\": \"Fotos do Imóvel (Externas do imóvel e das benfeitorias se existentes)\" }");
$templateCache.put("scripts-servicos/pages/requerimento-revigoracao-aforamento/views/detalharRequerimentoRevigoracaoAforamento.html","<barra-portal></barra-portal><div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><span class=\"texto-verde detalhar-label\" ng-show=exibirRepresentanteLegal(requerimento);>{{\'dados-representante-legal\' | translate}}</span><div ng-show=exibirRepresentanteLegal(requerimento);><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\">{{\'dados-imovel\' | translate}}</span><div><detalhar-imovel requerimento=requerimento></detalhar-imovel></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><div flex=100 class=md-block style=\"width: 100%\"><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/requerimento-revisao-valor-area/language/pt-BR.json","{ \"endereco-requerenet-igual-imovel\": \"O endereço do Requerente é o mesmo do Imóvel?\", \"complemento-imovel\": \"Complemento(Ex: nª apartamento, nome do prédio)\", \"indique-localizacao-mapa\": \"Indique no mapa a localização do imóvel para facilitar a análise do requerimento.\", \"usar-minha-localizacao\": \"Usar minha localização.\", \"add-foto\": \"Adicionar Foto\", \"uso-principal\": \"Uso principal\", \"especificacao-uso\": \"Especificação do Uso\", \"descricao-uso\": \"Descrição do Uso\", \"data-ocupacao\": \"Data de início da ocupação\", \"numero-familias\": \"Número de familías\", \"nome-rendimento-dependentes\": \"Relacione o nome e rendimento do(s) membro(s) da família que contribui(em) para a renda familiar.\", \"renda-mensal-uppercase\": \"RENDA MENSAL\", \"enviar-uppercase\": \"ENVIAR\", \"cancelar-uppercase\": \"CANCELAR\", \"possui-area-construida\": \"Possui área construída?\", \"area-construida\": \"Área Construída(m²)\", \"fotos-imovel\": \"Fotos do Imóvel (Externas do imóvel e das benfeitorias se existentes)\" }");
$templateCache.put("scripts-servicos/pages/requerimento-revisao-valor-area/views/detalharRequerimentoRevisaoValorArea.html","<barra-portal></barra-portal><div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><span class=\"texto-verde detalhar-label\" ng-show=exibirRepresentanteLegal(requerimento);>{{\'dados-representante-legal\' | translate}}</span><div ng-show=exibirRepresentanteLegal(requerimento);><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\">{{\'dados-imovel\' | translate}}</span><div><detalhar-imovel requerimento=requerimento tipo=REQUERIMENTO_REVISAO_VALOR_AREA></detalhar-imovel></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><div flex=100 class=md-block style=\"width: 100%\"><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/requerimento-solicitar-permissao-uso/language/pt-BR.json","{ \"Dados do Evento\":\"\", \"dados-evento\":\"DADOS EVENTO\", \"nome-evento\":\"Nome do Evento\", \"descricao-evento\":\"Descrição do Evento\", \"natureza\":\"Natureza\", \"informacao-economica-comercial\":\"Informações Ecônomica/Comercial\", \"periodo-utilizacao-area\":\"Periodo de Utilização da Área\", \"data-de-realizacao-evento\":\"Data de Realização do Evento\", \"data-inicial\":\"Data Inicial\", \"data-final\":\"Data Final\", \"prazo\":\"Prazo (dias)\", \"tipo-minusculo\":\"tipo\", \"Dados da Area do Evento \":\"\", \"dados-area-evento\":\"DADOS DA ÁREA DO EVENTO\", \"endereco-referencia\": \"Endereço de Referência\", \"area-abrangencia\":\"Área de abrangência do evento(Desenhe no mapa abaixo)\", \"area-exploracao-info\":\"A área de exploração economica corresponde a área total do evento\", \"area-exploracao-campo\":\"Área de exploração economica:\", \"planta\":\"Planta\", \"documentos\": \"DOCUMENTOS\", \"Dados do Documentos\":\"\", \"dados-documentos\":\"Documentos\", \"complementares\":\"Complementares\", \"obrigatorios\":\"Obrigatórios\", \"possuo\":\"Possuo\", \"nao-possuo\":\"Não Possuo\", \"nao-possuo-consegui\":\"Não possuo mas posso conseguir\", \"Informacao Socioeconomica\":\"\", \"Dados Modelo Placa\":\"\", \"modelo-placa\":\"MODELO PLACA FAIXA\", \"modelo-placa-pdf\":\"Modelo de Placa e Faixa\", \"Dados Correspondecia\":\"\", \"dados-correspondencia\":\"ENDEREÇO DE CORRESPONDÊNCIA\", \"Dados do Imvoel\":\"\", \"Dados do Regime\":\"\", \"dados-regime\":\"DADOS DO REGIME/CONTRATO\", \"regime-contrato\":\"Regime/Contrato Pretendido:\", \"uso-pretendido\":\"Uso Pretendido:\", \"descricao-uso\":\"Descrição do uso:\", \"Dados do Aquisicao\":\"\", \"dados-aquisicao\":\"FORMA DE AQUISICAO\", \"Termos\":\"\", \"declaro-conhecimento\":\"Declaro ter conhecimento que o presente requerimento fundamenta-se no disposto no art.22 da Lei 9636/1988 e no art.14 e seus parágrafos do Decreto 3725/2001.\", \"declaro-veracidade\":\"Declaro, sob pena da lei, que as informações prestadas e os documentos apresentados são verdadeiros.\", \"Botoes\":\"\", \"cancelar\":\"Cancelar\", \"salvar\":\"Salvar\", \"selecionar-arquivo\": \"Selecionar Arquivo\", \"selecionar-arquivos\": \"Selecionar Arquivos\", \"procurar\":\"Procurar\", \"adicionar\":\"Adicionar\", \"enviar\":\"Enviar\", \"confirmar\": \"Confirmar\", \"fechar\": \"Fechar\", \"acao\":\"Ação\", \"Comum\":\"\", \"sim\":\"Sim\", \"nao\":\"Não\", \"texto-falecido\":\"Atenção: O envio da Certidão de Óbito do Requerente é obrigatório para a análise desse requerimento\", \"selecione\":\"--Selecione--\", \"label-nome-rendimento-membro-familia\": \"Relacione o nome e rendimento do(s) membro(s) da família que contribui(em) para a renda familiar.\", \"label-dependentes-economicos\": \"Possui dependentes econômicos:\", \"label-renda-familiar-mensal\": \"Renda familiar mensal (R$):\", \"label-profissao\": \"Profissão:\", \"label-adicionar\": \"Adicionar\", \"label-excluir\": \"Excluir\", \"label-renda-mensal-tipo-modeda\": \"Renda Mensal (R$)\", \"label-alerta\": \"Alerta\", \"label-confirmacao\": \"Confirmação\", \"label-protocolo-atendimento\": \"Prezado usuário, sua solicitação foi recebida com sucesso. O número do seu Atendimento é {{parametro}}\", \"label-cpf\": \"CPF\", \"label-renda-mensal\": \"Renda Mensal\", \"label-acao\": \"ação\", \"label-pendencias\": \"Pendências\", \"label-analise-requerimento-realizada\": \"Análise do requerimento realizada em \", \"label-itens-nao-atendidos\": \"Itens não atendidos\", \"label-analise\": \"Análise\", \"label-justificativa-anotacao\": \"Justificativa/Anotação\", \"label-detalhamento-pendencia\": \"Detalhamento da Pendência\", \"label-avaliacao-dia\": \"Avaliação do dia \", \"mensagem-recaptcha\":\"É obrigatório o preenchimento do capcha.\", \"mensagem-confirmacao-email\": \"E-mails de contato não conferem\", \"mensagem-ctrl-bloqueado\": \"Não é permitido CTRL + C\", \"mensagem-dependente-existente\": \"Dependente já informado\", \"mensagem-confirmar-exclusao\": \"Deseja confirmar exclusão?\", \"mensagem-dados-invalidos\": \"Dados invalidos\", \"mensagem-um-telefone-obrigatorio\": \"É obrigatório informar um telefone.\", \"mensagem-um-telefone-obrigatorio-requerente\": \"Informar um Telefone nos Dados do Requerente\", \"mensagem-cep-cartorio-diferente-imovel\": \"O Cartório e Imóvel devem estar na mesma Unidade Federativa\", \"mensagem-tamanho-max-arquivo\": \"O arquivo não pode possuir mais de 5mb\", \"mensagem-arquivo-formato-invalido\": \"Arquivo em formato inválido.\", \"mensagem-documentos-obrigatorios\": \"Os documentos obrigatórios devem ser informados.\", \"mensagem-campos-obrigatorios\": \"Necessário preencher os campos obrigatórios\", \"msg-min-caracteres\": \"É obrigatório informar no minimo 10 caracteres no campo {{parametro}}\", \"msg-cancelamento\":\"Após o cancelamento, este requerimento não será mais analisado. Deseja continuar?\", \"objetivo-requerimento\": \"Objetivo do Requerimento\", \"descricao-objetivo\": \"Descrição do Objetivo\", \"cartorio-registro-imoveis\": \"O requerente é cartório de registro de imóveis?\", \"selecionar-cartorio\": \"Selecionar Cartório\", \"nome-cartorio\": \"Nome do Cartório\", \"selecao-cartorio\": \"Seleção do cartório\", \"campo-obrigatorio\": \"Campo de preenchimento obrigatório\", \"selecione-normal\": \"Selecione\", \"btn-limpar\": \"Limpar\", \"btn-fechar\": \"Fechar\", \"btn-visualizar-requerimento\": \"Visualizar Requerimento\", \"escolher-localizacao-imovel\": \"Escolha uma das opções abaixo para indicar a localização do imóvel\", \"marcar-ponto-mapa\": \"Marcar ponto no mapa\", \"desenhar-poligonal\": \"Desenhar poligonal do terreno no mapa\", \"anexar-geometria\": \"Anexar geometria do terreno\", \"arquivo-formato-shapefile\": \"Arquivos em formato shapefile da poligonal do terreno\", \"informar-coordenada\": \"Informar coordenadas geográfcas do acesso principal do imóvel\", \"latitude\": \"Latitude\", \"longitude\": \"Longitude\", \"fonte\": \"Fonte\", \"google-earth\": \"Google Earth\", \"google-maps\": \"Google Maps\", \"incluir-planta\": \"Incluir Planta\", \"posicao\": \"Posição\", \"descricao\": \"Descrição\", \"tipo-imovel\": \"Tipo do Imóvel\", \"possui-area-construida\": \"Possui área construída?\", \"area-construida\": \"Área Construída(m²)\", \"fotos-imovel\": \"Fotos do Imóvel (Externas do imóvel e das benfeitorias se existentes)\", \"adicionar-foto\": \"Adicionar Foto\", \"incluir-fotos-imovel\": \"Incluir Fotos Imóvel\", \"fotos\": \"Fotos\", \"maximo-foto\": \"São permitidas, no máximo, 4 fotos.\", \"formato-invalido\": \"Formato de arquivo inválido\", \"incluir-duas-fotos\": \"Incluir pelo menos 2 fotos\", \"relacione\": \"Relacione o nome e rendimento do(s) membro(s) da família que contribui(em) para a renda familiar.\", \"renda-maiusculo\": \"RENDA MENSAL\", \"tipo\": \"Tipo\", \"acao\": \"Ação\", \"anotacao\":\"Anotações\", \"iniciar-analise\":\"Iniciar Ánalise\", \"cancelar-requerimento\":\"Cancelar Requerimento\", \"confirmar-cancelamento\":\"Confirmar Cancelamento\", \"status-requerimento\": \"Status Requerimento\" }");
$templateCache.put("scripts-servicos/pages/requerimento-solicitar-permissao-uso/view/detalharRequerimentoPermissaoUso.html","<div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><span class=\"texto-verde detalhar-label\" ng-show=exibirRepresentanteLegal(requerimento);>{{\'dados-representante-legal\' | translate}}</span><div ng-show=exibirRepresentanteLegal(requerimento);><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\">{{\'dados-evento\' | translate}}</span><div><detalhar-evento requerimento=requerimento></detalhar-evento></div><span class=\"texto-verde detalhar-label\">{{\'dados-area-evento\' | translate}}</span><div><detalhar-area-evento requerimento=requerimento></detalhar-area-evento></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div><div layout=row><md-input-container flex=100 ng-show=requerimento.anotacoes><label>{{\'label-anotacoes\' | translate}}</label><textarea disabled ng-model=requerimento.anotacoes maxlength=800 md-maxlength=800 md-select-on-focus></textarea></md-input-container></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/requerimento-transferencia-titular/language/pt-BR.json","{ \"dados-do-transmitente\": \"Dados do Transmitente\" }");
$templateCache.put("scripts-servicos/pages/requerimento-transferencia-titular/view/detalharRequerimentoTransferenciaTitular.html","<div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-do-transmitente\' | translate | uppercase}}</span><div><detalhar-transmitente transmitente=requerimento.transmitente></detalhar-transmitente></div><span class=\"texto-verde detalhar-label\">{{\'dados-do-adquirente-titular\' | translate}}</span><detalhar-requerente requerimento=requerimento></detalhar-requerente><detalhar-transferencia-imovel requerimento=requerimento></detalhar-transferencia-imovel><span class=\"texto-verde detalhar-label\">{{\'dados-correspondencia\' | translate}}</span><detalhar-endereco-correspondencia requerimento=requerimento></detalhar-endereco-correspondencia><span class=\"texto-verde detalhar-label\" ng-show=possuiRepresentanteLegal();>{{\'dados-representante-legal\' | translate}}</span><div ng-show=possuiRepresentanteLegal();><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\">{{\'dados-imovel\' | translate}}</span><div><detalhar-imovel requerimento=requerimento></detalhar-imovel></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><md-input-container class=md-block flex style=\"width: 100%\" ng-show=requerimento.anotacoes><label>{{\'label-anotacoes\' | translate}}</label><textarea disabled ng-model=requerimento.anotacoes maxlength=800 md-maxlength=800 rows=5 md-select-on-focus flex></textarea></md-input-container><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/servico-generico-com-rip/language/pt-BR.json","{ }");
$templateCache.put("scripts-servicos/pages/servico-generico-com-rip/views/detalharServicoGenericoComRip.html","<barra-portal></barra-portal><div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><span class=\"texto-verde detalhar-label\">{{\'dados-correspondencia\' | translate}}</span><div><detalhar-endereco-correspondencia requerimento=requerimento></detalhar-endereco-correspondencia></div><span class=\"texto-verde detalhar-label\" ng-show=verificarExisteRepresententeLegal();>{{\'dados-representante-legal\' | translate}}</span><div ng-show=verificarExisteRepresententeLegal();><detalhar-representante-legal requerimento=requerimento tipo=\"\'CONSULTA_DOMINIALIDADE\'\"></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\" ng-show=exibirRepresentanteLegal(requerimento);>{{\'dados-representante-legal\' | translate}}</span><div ng-show=exibirRepresentanteLegal(requerimento);><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\">{{\'dados-imovel\' | translate}}</span><div><detalhar-imovel requerimento=requerimento tipo=\"\'GENERICO_VINCULACAO_SEM_RIP\'\"></detalhar-imovel></div><span class=\"texto-verde detalhar-label\">{{\'dados-complementar\' | translate}}</span><div ng-if=requerimento.dadoComplementares><dados-complementar campos-dados-complementares=requerimento.dadoComplementares></dados-complementar></div><span class=\"texto-verde detalhar-label\" ng-show=\"requerimento.arquivosAjuda.length > 0\">{{\'arquivos-ajuda\' | translate}}</span><div ng-if=requerimento.arquivosAjuda><arquivos-ajuda lista-arquivo=requerimento.arquivosAjuda></arquivos-ajuda></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><div flex=100 class=md-block style=\"width: 100%\"><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/servico-generico-sem-rip/language/pt-BR.json","{ }");
$templateCache.put("scripts-servicos/pages/servico-generico-sem-rip/views/detalharServicoGenericoSemRip.html","<barra-portal></barra-portal><div class=\"conteudo simbolo-lateral\"><toaster-container></toaster-container><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><detalhar-requerente requerimento=requerimento></detalhar-requerente></div><span class=\"texto-verde detalhar-label\">{{\'dados-correspondencia\' | translate}}</span><div><detalhar-endereco-correspondencia requerimento=requerimento></detalhar-endereco-correspondencia></div><span class=\"texto-verde detalhar-label\" ng-show=verificarExisteRepresententeLegal();>{{\'dados-representante-legal\' | translate}}</span><div ng-show=verificarExisteRepresententeLegal();><detalhar-representante-legal requerimento=requerimento tipo=\"\'CONSULTA_DOMINIALIDADE\'\"></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\" ng-show=exibirRepresentanteLegal(requerimento);>{{\'dados-representante-legal\' | translate}}</span><div ng-show=exibirRepresentanteLegal(requerimento);><detalhar-representante-legal requerimento=requerimento></detalhar-representante-legal></div><span ng-show=existeImovel class=\"texto-verde detalhar-label\">{{\'dados-imovel\' | translate}}</span><div ng-show=existeImovel><detalhar-imovel requerimento=requerimento tipo=\"\'GENERICO_VINCULACAO_SEM_RIP\'\"></detalhar-imovel></div><span class=\"texto-verde detalhar-label\">{{\'dados-complementar\' | translate}}</span><div ng-if=requerimento.dadoComplementares><dados-complementar campos-dados-complementares=requerimento.dadoComplementares></dados-complementar></div><span class=\"texto-verde detalhar-label\" ng-show=\"requerimento.arquivosAjuda.length > 0\">{{\'arquivos-ajuda\' | translate}}</span><div ng-if=requerimento.arquivosAjuda><arquivos-ajuda lista-arquivo=requerimento.arquivosAjuda></arquivos-ajuda></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><div layout=row><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><div flex=100 class=md-block style=\"width: 100%\" layout-margin=5><div layout=column layout-xs=column flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-anotacoes\' | translate}}</label></div><div layout=row><label layout-wrap ng-bind=requerimento.anotacoes flex></label></div></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/servicos/language/pt-BR.json","{ \"toltip-limpar-campos\": \"Limpar campos\", \"toltip-pesquiser-servicos\": \"Pesquisar serviços\", \"toltip-adicionar-servicos\": \"Adicionar serviços\", \"toltip-editar-solicitante\": \"Editar solicitante\", \"toltip-excluir-solicitante\": \"Excluir solicitante\", \"toltip-editar-documento\": \"Editar documento\", \"toltip-excluir-documento\": \"Excluir documento\", \"toltip-excluir-analise-tecnica\": \"Excluir item de análise técnica\", \"toltip-excluir-triagem\": \"Excluir item de triagem\", \"toltip-excluir-declaracao\": \"Excluir item de declaração\", \"label-servico\": \"Serviço\", \"label-nome-titulo\": \"Nome/Título\", \"label-identidicador-portal-governo\": \"Identificador no Portal do Governo Federal\", \"label-situacao\": \"Situação\", \"label-servicos\": \"Serviços\", \"label-data-virgencia\": \"Data de Vigência\", \"label-inicio-vigencia\": \"Início vigência\", \"label-subtitulo\": \"Subtítulo\", \"label-permissoes-acessos\": \"Permissão Acessos\", \"label-link\": \"Link\", \"label-inserir-link\": \"Inserir Link\", \"label-formulario-web\": \"Formulários Web\", \"label-unidade-atendente\": \"Unidade Atendente (SEI)\", \"upload-formulario\": \"Upload de formulário em PDF\", \"label-alerta\": \"Alerta\", \"selecionar-arquivo\": \"Selecionar arquivo\", \"label-grupo-processo-sei\":\"Grupo de Processo SEI\", \"label-assuntos\": \"Assuntos\", \"label-palavras-chaves\": \"Palavras Chaves\", \"label-delete-button-palavra-chave\": \"Remover Palavra Chave\", \"delete-hint-palavra-chave\":\"Delete uma palavra chave\", \"secondary-placeholder-palavra-chave\": \"+Palavra Chave\", \"label-solicitantes\": \"Solicitantes\", \"label-segmentos\": \"Segmentos\", \"label-requisitos\": \"Requisitos\", \"label-nome-documento\": \"Nome do documento\", \"label-solicitante-obrigatorio\":\"Solicitante\", \"label-solicitante\":\"Solicitante\", \"label-segmento\": \"Segmento\", \"label-requisito-obrigatorio\": \"Requisitos\", \"label-tipo-obrigatorio\": \"Tipo\", \"label-documentos\": \"Documentos\", \"label-uf-unidade-atendimento\": \"De qual(is) Unidade(s) de Atendimento?\", \"label-uf-combo\" : \"Selecione as UFs\", \"label-email-complementar\": \"Email: \", \"label-combo-cep\": \"CEP\", \"label-checklist-analise-tecnica\": \"Checklist de Análise Técnica\", \"label-checklist-triagem\": \"Checklist de Triagem\", \"label-declaracoes\": \"Declarações\", \"label-item-checklist-validacao-requerimento\": \"Item do Checklist de Validação do Requerimento\", \"label-declaracao\": \"Declaração\", \"mensagem-arquivo-formato-invalido\" : \"Arquivo em formato inválido.\", \"label-exclusao\": \"Excluir Arquivo\", \"label-tipo-processo\": \"Tipo Processo SEI\", \"btn-adicionar-solicitante\": \"Adicionar Solicitante\", \"btn-adicionar-documento\": \"Adicionar Documento\", \"btn-adicionar-item-checklist\": \"Adicionar Item do Checklist\", \"btn-adicionar-declaracao\": \"Adicionar Declaração\", \"label-extensao\":\"Extensões:\", \"extensao\":\"Adicionar uma nova Extensão:\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-novo\": \"Novo\", \"label-titulo\": \"Editar\", \"error-item-analise-tecnica\": \"É necessário informar o campo de Item da Análise Técnica.\", \"error-item-triagem\": \"É necessário informar o campo de Item da Triagem.\", \"error-min-caracteres\": \"É necessário informar no mínimo {{max}} caracteres\", \"abrir-iframe\": \"Deseja utilizar este link como iframe?\", \"error-informe-tipo\": \"É necessário informar o campo Tipo\", \"error-min-campo\": \"É necessário no mínimo {{min}} caracteres no campo de {{campo}}\", \"error-max-campo\": \"O campo de {{campo}} possui tamanho máximo de {{max}} caracteres\", \"error-informe-solicitante\": \"É necessário informar um Solicitante\", \"incluir-etapa-triagem\": \" Incluir etapa de triagem\", \"pressione-enter\": \" Pressione Enter para salvar a palavra\", \"selecione\": \"-- Selecione --\", \"checklist-analise_tecnica\": \"Checklist Análise Técnica\", \"checklist-triagem\": \"Checklist de Triagem\", \"marcacao-obrigatoria\": \"Marcação obrigatória\", \"mensagem-confirmar-exclusao\": \"Deseja confirmar exclusão?\", \"dados-complementares\": \"Dados Complementares\", \"arquivos-ajuda\": \"Arquivos de Ajuda\", \"arquivo-ajuda\": \"Arquivo de Ajuda\", \"tipo-campo\": \"Tipo do Campo\", \"combos\": \"Combos\", \"componentes\": \"Componentes\", \"nome-campo\": \"Nome do Campo\", \"nome-arquivo\": \"Nome do Arquivo\", \"valor-combo\": \"Valor do Combo\", \"upload-ajuda\": \"Upload de Arquivo de Ajuda\", \"obrigatoriedade\": \"Obrigatoriedade\", \"tamanho-campo\": \"Tamanho do campo\", \"mais-incluir-valor\": \" +Incluir Valor\", \"excluir-dados\": \"Excluir Dados Complementar\", \"editar-dados\": \"Editar Dados Complementar\", \"n-a\": \"N/A\", \"retirar-dados-imovel\": \"Retirar campos referentes ao imóvel\", \"servico-unidade-central\": \"Serviço da Unidade Central\", \"label-configuracao\": \"Configurações\", \"label-habilitar-Priorizacao-Atendimento\":\"Habilitar priorização de atendimento\", \"exigir-autenticacao-brasil-cidadao\":\"Exigir autenticação com Brasil Cidadão\", \"nivel-autenticacao-brasil-cidadao\":\"Qual nível de autenticação?\", \"conclusao-servico-triagem\": \"Habilitar conclusão do serviço na triagem\", \"conclusao-com-processo\": \"Com processo\", \"conclusao-sem-processo\": \"Sem processo\", \"subsecao\": \"Subseção\", \"adicionar-subsecao\": \"Adicionar Subseção\", \"nome-subsecao\": \"Nome da Subseção\", \"campo-subsecao\": \"Campo da Subseção\", \"campos-subsecao\": \"Campos da Subseção\" }");
$templateCache.put("scripts-servicos/pages/servicos/view/adicionarArquivosAjuda.html","<md-content flex><div layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised style=\"margin-left: 0\" ng-click=abrirModalArquivosAjuda($event)>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-if=\"listas.arquivosAjuda.length > 0\" class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column></th><th md-column class=texto-centro>{{\'nome-campo\' | translate}}</th><th md-column class=texto-centro>{{\'nome-arquivo\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in listas.arquivosAjuda | limitTo: queryDados.limit: (queryDados.page - 1) * queryDados.limit track by $index\"><td md-cell md-cell class=texto-centro-corpo style=\"width: 1px; padding: 0 !important\"><p style=\"margin: 0\"><md-button class=\"material-icons botao-posicao\" ng-click=subirPosicao($index)>keyboard_arrow_up</md-button><md-button class=\"material-icons botao-posicao\" ng-click=descerPosicao($index)>keyboard_arrow_down</md-button></p></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.nomeCampo}}</p></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.arquivos[0].nome}}</p></td><td md-cell class=\"texto-centro-corpo padding-right-0\"><a href=\"\" ng-click=\"abrirModalArquivosAjuda($event, item)\" style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"{{\'editar-dados\' | translate}}\"><md-tooltip>{{\'editar-dados\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=\"removerArquivosAjuda($event, item);\" style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'excluir-dados\' | translate}}\"><md-tooltip>{{\'excluir-dados\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listas.arquivosAjuda.length > 0\" md-limit=queryDados.limit md-limit-options=queryDados.limitsPage md-page=queryDados.page md-total={{listas.arquivosAjuda.length}} md-page-select=true></md-table-pagination></md-content></div></md-content>");
$templateCache.put("scripts-servicos/pages/servicos/view/adicionarDadosComplementares.html","<md-content flex><div layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised style=\"margin-left: 0\" ng-click=abrirModalDadosComplementares($event)>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-if=\"listas.dadosComplementar.length > 0\" class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column></th><th md-column class=texto-centro>{{\'nome-campo\' | translate}}</th><th md-column class=texto-centro>{{\'tipo-campo\' | translate}}</th><th md-column class=texto-centro>{{\'tamanho-campo\' | translate}}</th><th md-column class=texto-centro>{{\'obrigatoriedade\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in listas.dadosComplementar | limitTo: queryDados.limit: (queryDados.page - 1) * queryDados.limit track by $index\"><td md-cell md-cell class=texto-centro-corpo style=\"width: 1px; padding: 0 !important\"><p style=\"margin: 0\"><md-button class=\"material-icons botao-posicao\" ng-click=subirPosicaoDadoComplementar($index)>keyboard_arrow_up</md-button><md-button class=\"material-icons botao-posicao\" ng-click=descerPosicaoDadoComplementar($index)>keyboard_arrow_down</md-button></p></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.nomeCampo}}</p></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.tipoDadoComplementar.descricao}}</p></td><td md-cell md-cell class=texto-centro-corpo><p>{{isNumeroInteiro(item.tamanhoCampo) ? item.tamanhoCampo : \'n-a\' | translate }}</p></td><td md-cell md-cell><p>{{item.obrigatorio ? \'sim\': \'nao\' | translate}}</p></td><td md-cell class=\"texto-centro-corpo padding-right-0\"><a href=\"\" ng-click=\"abrirModalDadosComplementares($event, item)\" style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"{{\'editar-dados\' | translate}}\"><md-tooltip>{{\'editar-dados\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=\"removerDadosComplementares($event, item);\" style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'excluir-dados\' | translate}}\"><md-tooltip>{{\'excluir-dados\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listas.dadosComplementar.length > 0\" md-limit=queryDados.limit md-limit-options=queryDados.limitsPage md-page=queryDados.page md-total={{listas.dadosComplementar.length}} md-page-select=true></md-table-pagination></md-content></div></md-content>");
$templateCache.put("scripts-servicos/pages/servicos/view/adicionarDeclaracao.html","<!--<div class=\"texto-teste-dois\">{{\'label-declaracoes\' | translate}}*</div>--><md-content flex><div layout=row layout-sm=column layout-align=end layout-wrap><md-button id=adicionarDeclaracao class=\"md-raised declaracao\" style=\"margin-left: 0\" ng-click=modalDeclaracoes()>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-if=\"listas.declaracao.length > 0\" class=top-1><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-item\' | translate}}</th><th md-column class=texto-centro>{{\'label-solicitantes\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in listas.declaracao | orderBy: \'ordem\' | limitTo: queryDeclaracoes.limit: (queryDeclaracoes.page - 1) * queryDeclaracoes.limit\"><td md-cell class=texto-centro-corpo><div ng-bind-html=\"item.item | to_trusted\"></div></td><td md-cell class=texto-centro-corpo><p ng-repeat=\"i in item.solicitantes\" ng-bind=i.tipo>{{i.tipo}}</p></td><td md-cell class=texto-centro-corpo><ng-md-icon icon=delete style=fill:#788489 size=22 alt=toltip-excluir-declaracao ng-click=removerDeclaracoes($event,item);><md-tooltip>toltip-excluir-declaracao</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listas.declaracao.length > 0\" md-limit=queryDeclaracoes.limit md-limit-options=queryDeclaracoes.limitsPage md-page=queryDeclaracoes.page md-total={{listas.declaracao.length}} md-page-select=true></md-table-pagination></div></div></md-content>");
$templateCache.put("scripts-servicos/pages/servicos/view/adicionarDocumentos.tmpl.html","<!--<div class=\"texto-teste-dois\">{{\'label-documentos\' | translate}}</div>--><md-content flex><div layout=row layout-sm=column layout-align=end layout-wrap><md-button id=adicionarDocumentos class=md-raised style=\"margin-left: 0\" ng-click=editDocumento($event)>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-show=\"listas.documento.length > 0\" class=top-1><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-nome\' | translate}}</th><th md-column class=texto-centro>{{\'label-solicitantes\' | translate}}</th><th md-column class=texto-centro>{{\'label-situacao\' | translate}}</th><th md-column class=texto-centro>{{\'label-tipo-documento\' | translate}}</th><th md-column class=texto-centro>{{\'label-extensao\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in listas.documento | orderBy: \'ordem\' | limitTo: queryDocumento.limit: (queryDocumento.page - 1)\n                             * queryDocumento.limit\"><td md-cell class=texto-centro-corpo>{{item.nome}}</td><td md-cell class=texto-centro-corpo><p ng-repeat=\"i in item.solicitantes\" ng-bind=i.tipo>{{i.tipo}}</p></td><td md-cell md-cell class=texto-centro-corpo><p ng-repeat=\"tipoDoc in tiposDocumento\" ng-if=\"tipoDoc.chave==item.tipo\">{{tipoDoc.valor}}</p></td><td md-cell md-cell class=texto-centro-corpo><p ng-repeat=\"listatiposDoc in listaTiposDocumento\" ng-if=\"listatiposDoc.id==item.tipoDocumento.id\">{{listatiposDoc.nome}}</p></td><td md-cell md-cell class=texto-centro-corpo><p ng-repeat=\"formato in item.formatoArquivos\">{{ \'.\' + (formato.formato | lowercase) }}</p></td><td md-cell class=texto-centro-corpo><a href=\"\" ng-click=\"editDocumento($event, item)\" style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"Editar documento\"><md-tooltip>Editar documento</md-tooltip></ng-md-icon></a><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"Excluir documento\" ng-click=\"removerDocumentos($event, item);\"><md-tooltip>Excluir documento</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listas.documento.length > 0\" md-limit=queryDocumento.limit md-limit-options=queryDocumento.limitsPage md-page=queryDocumento.page md-total={{listas.documento.length}} md-page-select=true></md-table-pagination></div></div></md-content>");
$templateCache.put("scripts-servicos/pages/servicos/view/adicionarSolicitante.tmpl.html","<!--<div class=\"texto-teste-dois\">{{\'label-solicitante\' | translate}}*</div>--><div flex><div layout=row layout-sm=column layout-align=end layout-wrap><md-button id=adicionarSolicitante class=md-raised style=\"margin-left: 0\" ng-click=editSolicitante($event) id=adicionarSolicitante>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-show=\"listas.solicitante.length > 0\" class=top-1><div flex><md-table-container><table md-table><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro>{{\'label-tipo\' | translate}}</th><th md-column class=texto-centro>{{\'label-segmentos\' | translate}}</th><th md-column class=texto-centro>{{\'label-requisitos\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in listas.solicitante | orderBy: querySolicitante.order | limitTo: querySolicitante.limit: (querySolicitante.page - 1) * querySolicitante.limit\"><td md-cell class=texto-centro-corpo>{{item.tipo}}</td><td md-cell class=texto-centro-corpo><p ng-repeat=\"i in item.segmento\" ng-bind=i.descricao>{{i.descricao}}</p></td><td md-cell class=texto-centro-corpo>{{item.requisitos}}</td><td md-cell class=texto-centro-corpo><a href=\"\" ng-click=\"editSolicitante($event, item)\" style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"Editar serviço\"><md-tooltip>Editar solicitante</md-tooltip></ng-md-icon></a><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"Excluir solicitante\" ng-click=\"removerSolicitante($event, item);\"><md-tooltip>Excluir solicitante</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listas.solicitante.length > 0\" ng-show=\"listas.solicitante.length > 0\" md-limit=querySolicitante.limit md-limit-options=querySolicitante.limitsPage md-page=querySolicitante.page md-total={{listas.solicitante.length}} md-page-select=true></md-table-pagination></div></div></div>");
$templateCache.put("scripts-servicos/pages/servicos/view/adicionarSubsecao.html","<md-content flex><div layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised style=\"margin-left: 0\" ng-click=abrirModalSubsecoes($event)>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-if=\"listas.subsecoesDadoComplementar.length > 0\" class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column></th><th md-column class=texto-centro>{{\'nome-campo\' | translate}}</th><th md-column class=texto-centro>{{\'campos-subsecao\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in listas.subsecoesDadoComplementar | limitTo: queryDadosSubsecao.limit: (queryDadosSubsecao.page - 1) * queryDadosSubsecao.limit track by $index\"><td md-cell md-cell class=texto-centro-corpo style=\"width: 1px; padding: 0 !important\"><p style=\"margin: 0\"><md-button class=\"material-icons botao-posicao\" ng-click=subirPosicaoSubsecao($index)>keyboard_arrow_up</md-button><md-button class=\"material-icons botao-posicao\" ng-click=descerPosicaoSubsecao($index)>keyboard_arrow_down</md-button></p></td><td md-cell class=texto-centro-corpo><p>{{item.nome}}</p></td><td md-cell class=texto-centro-corpo><p><span ng-repeat=\"campo in item.campos track by $index\" ng-if=campo.status>{{campo.nomeCampo}};</span></p></td><td md-cell class=\"texto-centro-corpo padding-right-0\"><a href=\"\" ng-click=\"abrirModalSubsecoes($event, item)\" style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"{{\'editar-dados\' | translate}}\"><md-tooltip>{{\'editar-dados\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=\"removerSubsecoes($event, item);\" style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'excluir-dados\' | translate}}\"><md-tooltip>{{\'excluir-dados\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listas.subsecoesDadoComplementar.length > 0\" md-limit=queryDados.limit md-limit-options=queryDados.limitsPage md-page=queryDados.page md-total={{listas.subsecoesDadoComplementar.length}} md-page-select=true></md-table-pagination></md-content></div></md-content>");
$templateCache.put("scripts-servicos/pages/servicos/view/cadastrarServicos.tmpl.html","<div class=standard><titulo-servico dinamico=!(isNew) titulo-statico=tituloStatico class=texto-principal></titulo-servico><form name=form ng-submit=\"validar() && salvar()\"><div layout-margin=5 layout=row layout-xs=column><md-input-container flex><label>{{\'label-nome-titulo\' | translate}}</label><input ng-model=servico.titulo md-maxlength=100 maxlength=100 name=\"{{\'label-nome-titulo\' | translate}}\" ng-required=true></md-input-container></div><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-subtitulo\' | translate}}</label><input ng-model=servico.subtitulo md-maxlength=400 maxlength=400 name=\"{{\'label-subtitulo\' | translate}}\" required></md-input-container><md-input-container flex><label>{{\'label-permissoes-acessos\' | translate}}</label><input ng-model=servico.permissaoAcessos md-maxlength=150 maxlength=150 name=codigoSistemico required><div ng-messages=form.codigoSistemico.$error><div ng-if=\"form.codigoSistemico.$error.required && form.codigoSistemico.$dirty\"><small style=\"color:rgb(221,44,0) !important\">Campo obrigatório</small></div><div ng-if=\"form.codigoSistemico.$error.md-maxlength == true\"><small style=\"color:rgb(221,44,0) !important\">O campo deve ter no máximo 150 caracteres</small></div></div></md-input-container></div><div layout=row layout-margin=5 class=margem-link><md-input-container flex class=margem-link><label>{{\'label-inserir-link\' | translate}}</label><input ng-model=servico.link md-maxlength=100 maxlength=100 ng-required=!(servico.formularioWeb) ng-disabled=isFormulario() name=\"{{\'label-link\' | translate}}\"></md-input-container><md-input-container flex><label>{{\'label-identidicador-portal-governo\' | translate}}</label><input ng-model=servico.idPortalGovernoFederal ng-maxlength=5 maxlength=5 type=text onkeypress=\"return event.charCode >= 48 && event.charCode <= 57\" name=identificador ng-change=\"mostrarResultado(servico.idPortalGovernoFederal,5,\'spanContando\')\"> <span id=spanContando style=\"float: right\">{{verificaDigitos(servico.idPortalGovernoFederal)}} / 5</span></md-input-container></div><div class=\"row iframe-margem\" layout-margin=5 ng-if=servico.link><md-input-container flex class=iframe-link><md-checkbox ng-model=servico.utilizarIframe ng-value=servico.utilizarIframe class=md-primary>{{\'abrir-iframe\' | translate}}</md-checkbox></md-input-container></div><div layout=row layout-xs=column layout-margin=5><md-input-container flex class=formulario><label>{{\'label-formulario-web\' | translate}}</label><md-select ng-model=servico.formularioWeb ng-disabled=servico.link ng-required=!servico.link ng-model-options=\"{trackBy: \'$value.id\'}\" ng-change=selecionarFormulario() name=\"{{\'label-formulario-web\' | translate}}\" id=formularioAbrir><md-option value={{null}}>{{\'selecione\' | translate}}</md-option><md-option ng-repeat=\"formulario in formularios\" ng-value={{formulario}}>{{formulario.nomeFormulario}}</md-option></md-select></md-input-container><md-input-container flex><label>{{\'label-assuntos\' | translate}}</label><md-select ng-model=servico.assunto ng-required=true ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-assuntos\' | translate}}\"><md-option value={{null}}>{{\'selecione\' | translate}}</md-option><md-option ng-repeat=\"assunto in assuntos\" ng-value={{assunto}}>{{assunto.descricao}}</md-option></md-select></md-input-container></div><div><div class=\"row iframe-margem checkbox-cadastrar\" layout-margin=5><md-input-container style=\"margin-left: 5px\"><md-checkbox ng-model=servico.brasilCidadao.exigirAutenticacaoBrasilCidadao ng-change=checkBrasil() ng-disabled=\"servico.formularioWeb.rota==PARCELAMENTO_DEBITOS\" class=md-primary>{{\'exigir-autenticacao-brasil-cidadao\' | translate}}</md-checkbox></md-input-container></div><div class=\"row iframe-margem checkbox-cadastrar\" layout-margin=5 ng-if=servico.brasilCidadao.exigirAutenticacaoBrasilCidadao><md-input-container style=\"margin-left: 5px; margin-bottom: 2em\" flex=50><label>{{\'nivel-autenticacao-brasil-cidadao\' | translate}}</label><md-select ng-model=servico.brasilCidadao.nivelAutenticacao><md-option ng-selected=!servico.brasilCidadao.nivelAutenticacao value=\"\">{{\'selecione\' | translate}}</md-option><md-option ng-repeat=\"(index, value) in listaNiveisAutenticacao\" value={{index}}>{{value}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column ng-show=\"servico.formularioWeb.rota==SERVICO_GENERICO_SEM_RIP && servico.formularioWeb.id!=ID_SERVICO_GENERICO_SEM_RIP_VENAL\"><md-input-container flex=50><md-checkbox ng-model=servico.retirarDadosImovel class=md-primary style=margin-left:12px>{{\'retirar-dados-imovel\' | translate}}</md-checkbox></md-input-container></div><div class=\"row checkbox-cadastrar\" layout-margin=5 ng-if=\"servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><md-input-container style=\"margin-left: 5px\"><md-checkbox ng-model=$parent.concluirServicoTriagem ng-change=alteraConclusaoTriagem() class=md-primary>{{\'conclusao-servico-triagem\' | translate}}</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-if=concluirServicoTriagem><md-radio-group style=margin-left:100px class=md-primary ng-model=servico.conclusaoServicoTriagem required><md-radio-button ng-value=true>{{\'conclusao-com-processo\' | translate}}</md-radio-button><md-radio-button ng-value=false>{{\'conclusao-sem-processo\' | translate}}</md-radio-button></md-radio-group></div><div class=\"row iframe-margem checkbox-cadastrar\" layout-margin=5 ng-if=\"servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><md-input-container style=\"margin-left: 5px\"><md-checkbox ng-model=servico.habilitarPriorizacaAtendimento class=md-primary>{{\'label-habilitar-Priorizacao-Atendimento\' | translate}}</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-if=\"servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><md-input-container style=\"margin-left: 10px\" flex=50><md-checkbox ng-model=servico.unidadeCentral class=md-primary>{{\'servico-unidade-central\' | translate}}</md-checkbox></md-input-container></div><div layout=row layout-margin=5 layout-xs=column style=\"margin-left: 14px\" ng-if=\"servico.unidadeCentral==true\"><md-input-container flex=50>{{searchItem}}<label>{{\'label-uf-unidade-atendimento\' | translate}}</label><md-select ng-model=servico.listUnidadeCentral ng-required=\"servico.unidadeCentral==true\" md-on-close=clearSearchItem() data-md-container-class=selectdemoSelectHeader multiple><md-select-header class=demo-select-header><input ng-model=searchItem type=search placeholder=UF class=\"demo-header-searchbox md-text\"></md-select-header><md-optgroup label=UF><md-option ng-value=item ng-repeat=\"item in listJurisdicoes |\n                      filter:searchItem | orderBy\">{{item}}</md-option></md-optgroup></md-select></md-input-container><md-button class=md-primary ng-click=\"servico.listUnidadeCentral=listJurisdicoes\">Todos</md-button><md-button class=md-primary ng-click=\"servico.listUnidadeCentral=[]\">Nenhum</md-button></div><div layout-margin=5 class=prazoAtendimento ng-if=\"servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><label><b>Prazo de atendimento:</b></label><div layout=row layout-margin=5><div flex=50><label>Definir prazo de triagem</label><md-radio-group layout-margin=5 required ng-model=servico.prazoAtendimentoTriagem.tipoPrazoAtendimento class=md-primary><md-radio-button ng-value=\"\'SEM_PRAZO_DEFINIDO\'\" ng-click=limparPrazo(servico.prazoAtendimentoTriagem)>Sem prazo definido</md-radio-button><md-radio-button ng-value=\"\'PRAZO_DIAS_UTEIS\'\">Prazo em dias úteis</md-radio-button><div layout=column ng-if=\"servico.prazoAtendimentoTriagem.tipoPrazoAtendimento == \'PRAZO_DIAS_UTEIS\'\"><md-input-container flex class=margem-link><label>Quantidade</label><input ui-number-mask=0 ng-model=servico.prazoAtendimentoTriagem.quantidade maxlength=3 required name=\"{{\'label-link\' | translate}}\"></md-input-container><md-input-container flex class=margem-link><label>Justificativa</label><input ng-model=servico.prazoAtendimentoTriagem.justificativa maxlength=300 md-maxlength=300 required name=\"{{\'label-link\' | translate}}\"></md-input-container></div><md-radio-button ng-value=\"\'PRAZO_DIAS_CORRIDOS\'\">Prazo em dias corridos</md-radio-button><div layout=column ng-if=\"servico.prazoAtendimentoTriagem.tipoPrazoAtendimento == \'PRAZO_DIAS_CORRIDOS\'\"><md-input-container flex class=margem-link><label>Quantidade</label><input ui-number-mask=0 ng-model=servico.prazoAtendimentoTriagem.quantidade maxlength=3 required name=\"{{\'label-link\' | translate}}\"></md-input-container><md-input-container flex class=margem-link><label>Justificativa</label><input ng-model=servico.prazoAtendimentoTriagem.justificativa maxlength=300 md-maxlength=300 required name=\"{{\'label-link\' | translate}}\"></md-input-container></div></md-radio-group></div><div flex=50><label>Definir prazo de Análise Técnica</label><md-radio-group layout-margin=5 required ng-model=servico.prazoAtendimentoAnaliseTecnica.tipoPrazoAtendimento class=md-primary><md-radio-button ng-value=\"\'SEM_PRAZO_DEFINIDO\'\" selected ng-click=limparPrazo(servico.prazoAtendimentoAnaliseTecnica)>Sem prazo definido</md-radio-button><md-radio-button ng-value=\"\'PRAZO_DIAS_UTEIS\'\">Prazo em dias úteis</md-radio-button><div layout=column ng-if=\"servico.prazoAtendimentoAnaliseTecnica.tipoPrazoAtendimento == \'PRAZO_DIAS_UTEIS\'\"><md-input-container flex class=margem-link ng-class=\"{\'md-input-invalid\': servico.prazoAtendimentoAnaliseTecnica.quantidade < servico.prazoAtendimentoTriagem.quantidade}\"><label>Quantidade</label><input ui-number-mask=0 ng-model=servico.prazoAtendimentoAnaliseTecnica.quantidade ng-class=\"{\'ng-invalid\': servico.prazoAtendimentoAnaliseTecnica.quantidade < servico.prazoAtendimentoTriagem.quantidade}\" maxlength=3 required name=\"{{\'label-link\' | translate}}\"></md-input-container><md-input-container flex class=margem-link><label>Justificativa</label><input ng-model=servico.prazoAtendimentoAnaliseTecnica.justificativa maxlength=300 md-maxlength=300 required name=\"{{\'label-link\' | translate}}\"></md-input-container></div><md-radio-button ng-value=\"\'PRAZO_DIAS_CORRIDOS\'\">Prazo em dias corridos</md-radio-button><div layout=column ng-if=\"servico.prazoAtendimentoAnaliseTecnica.tipoPrazoAtendimento == \'PRAZO_DIAS_CORRIDOS\'\"><md-input-container flex class=margem-link ng-class=\"{\'md-input-invalid\': servico.prazoAtendimentoAnaliseTecnica.quantidade < servico.prazoAtendimentoTriagem.quantidade}\"><label>Quantidade</label><input ui-number-mask=0 ng-model=servico.prazoAtendimentoAnaliseTecnica.quantidade ng-class=\"{\'ng-invalid\': servico.prazoAtendimentoAnaliseTecnica.quantidade < servico.prazoAtendimentoTriagem.quantidade}\" maxlength=3 required name=\"{{\'label-link\' | translate}}\"></md-input-container><md-input-container flex class=margem-link><label>Justificativa</label><input ng-model=servico.prazoAtendimentoAnaliseTecnica.justificativa maxlength=300 md-maxlength=300 required name=\"{{\'label-link\' | translate}}\"></md-input-container></div></md-radio-group></div></div></div><label style=\"padding-left: 18px\">{{\'label-descricao\' | translate}}</label><div layout=row layout-margin=5><md-input-container flex=100 class=textangular><div text-angular ng-model=servico.descricao name=descricao ta-max-text=2000 ta-toolbar-button-class=\"btn btn-default\" ta-toolbar-button-active-class=active name=\"{{\'label-descricao\' | translate}}\"></div></md-input-container></div><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-palavras-chaves\' | translate}} {{\'*\'}}<md-tooltip>{{\'pressione-enter\' | translate}}</md-tooltip></label><md-chips ng-model=palavrasChaves ng-required=true ng-class=\"{\'md-clip-invalido\': isSemPalavraChave()}\" class=validador readonly name=\"{{\'label-palavras-chaves\' | translate}}\" delete-button-label=\"{{\'label-delete-button-palavra-chave\' | translate}}\" delete-hint=\"{{\'delete-hint-palavra-chave\' | translate}}\" secondary-placeholder=\"{{\'secondary-placeholder-palavra-chave\' | translate}}\" placeholder=\"Pressione Enter\"></md-chips></md-input-container></div><div layout=row ng-if=isFormulario() layout-margin=5><md-input-container flex=50 ng-if=\"servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><label>{{\'label-grupo-processo-sei\' | translate}}</label><md-select ng-model=servico.unidadeSEI ng-disabled=!servico.etapaTriagem ng-required=servico.etapaTriagem ng-model-options=\"{trackBy: \'$value.id\'}\" name=\"{{\'label-grupo-processo-sei\' | translate}}\"><md-option ng-value={{grupo}} ng-repeat=\"grupo in gruposSei\">{{grupo.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50 ng-show=\"servico.formularioWeb.rota==REQUERIMENTO_CONSULTA_DOMINIALIDADE || servico.formularioWeb.rota==REQUERIMENTO_GESTAO_PRAIAS\"><md-checkbox ng-model=servico.etapaTriagem ng-selected=servico.etapaTriagem aria-label=\"Incluir etapa de triagem\" ng-change=selecionarEtapaTriagem() class=md-primary>{{\'incluir-etapa-triagem\' | translate}}</md-checkbox></md-input-container></div><!--div layout=\"row\" layout-xs=\"column\" layout-margin=\"5\">\n      <md-input-container flex>\n        <label>{{\'label-tipo-processo\' | translate}}</label>\n        <md-select ng-model=\"servico.idTipoProcessoSei\" aria-label=\"Tipo Processo SEI\" required=\"true\">\n          <md-option ng-repeat=\"tipo in listTipos | orderBy\" value=\"{{tipo.id}}\">{{tipo.descricao}}</md-option>\n        </md-select>        \n      </md-input-container>\n    </div--><div layout=row layout-xs=column layout-margin=5><md-input-container flex><label>{{\'label-tipo-processo\' | translate}}</label><md-autocomplete md-no-cache=true md-selected-item=tipoSei md-items=\"tipoSei in filtraTipoProcesso(filtro)\" md-search-text=filtro md-item-text=tipoSei.descricao md-min-length=0 required><span>{{ tipoSei.descricao }}</span></md-autocomplete></md-input-container></div><div layout=row ng-if=isFormulario() layout-margin=5 ng-if=\"servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><md-input-container flex=50><label>{{\'upload-formulario\' | translate}}</label><input ng-model=servico.arquivo.name name=\"{{\'upload-formulario\' | translate}}\" disabled></md-input-container><md-button ngf-select=\"addDocumentos($files, servico, true)\" ngf-multiple=true class=md-raised style=\"height: 36px\">{{\'selecionar-arquivo\' | translate}}</md-button></div><div ng-if=\"servico.arquivo.name != undefined || servico.arquivo.name != null\" layout=row><div style=\"padding-left: 18px; padding-top: 5px\">{{servico.arquivo.name}}</div><a href=\"\" ng-click=\"removerArquivo($event, servico.arquivo.id)\"><ng-md-icon icon=delete style=fill:#788489></ng-md-icon></a></div><md-content ng-if=\"isFormulario() || servico.link != undefined\"><md-tabs md-dynamic-height md-border-bottom><md-tab label=\"{{\'solicitante\' | translate}} {{servico.link == undefined ? \'*\' : \'\'}}\"><md-content class=md-padding><div layout=row flex ng-include=\"\'scripts-servicos/pages/servicos/view/adicionarSolicitante.tmpl.html\'\"></div></md-content></md-tab><md-tab label=\"{{\'dados-documentos\' | translate}}\" ng-if=\"servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><md-content class=md-padding><div layout=row flex ng-include=\"\'scripts-servicos/pages/servicos/view/adicionarDocumentos.tmpl.html\'\"></div></md-content></md-tab><md-tab label=\"{{\'checklist-triagem\' | translate}}\" cd ng-disabled=!isFormulario() ng-if=\"servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><md-content class=md-padding><adicionar-checklist layout=row flex ng-model=listas.triagem checklists=servico.triagens label-nome=\"\'triagem\' | translate\" nome-checklist=\"\'checklist-triagem\' | translate | uppercase\" label-excluir-item-checklist=\"\'excluir-item-checklist-triagem\' | translate\"></adicionar-checklist></md-content></md-tab><md-tab id=declaracoesTst label=\"{{\'declaracoes\' | translate}} {{servico.link == undefined ? \'*\' : \'\'}}\" ng-disabled=!isFormulario()><md-content class=md-padding><div layout=row flex ng-include=\"\'scripts-servicos/pages/servicos/view/adicionarDeclaracao.html\'\"></div></md-content></md-tab><md-tab label=\"{{\'checklist-analise-tecnica\' | translate}}\" ng-disabled=!isFormulario() ng-if=\"servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><md-content class=md-padding><adicionar-checklist layout=row flex ng-model=listas.analiseTecnica checklists=servico.analisesTecnicas label-nome=\"\'analise-tecnica\' | translate\" nome-checklist=\"\'checklist-analise-tecnica\' | translate | uppercase\" label-excluir-item-checklist=\"\'excluir-item-checklist-analise-tecnica\' | translate\"></adicionar-checklist></md-content></md-tab><md-tab label=\"{{\'dados-complementares\' | translate}}\" ng-if=\"servico.formularioWeb && isDadosComplementar(servico.formularioWeb)\"><md-content class=md-padding ng-if=\"servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><div layout=row flex ng-include=\"\'scripts-servicos/pages/servicos/view/adicionarDadosComplementares.html\'\"></div></md-content></md-tab><md-tab label=Subseção ng-if=\"servico.formularioWeb && isSubsecao(servico.formularioWeb)\"><md-content class=md-padding><div layout=row flex ng-include=\"\'scripts-servicos/pages/servicos/view/adicionarSubsecao.html\'\"></div></md-content></md-tab><md-tab label=\"{{\'arq-ajuda\' | translate}}\" ng-if=\"servico.formularioWeb && servico.formularioWeb.rota!=PARCELAMENTO_DEBITOS\"><md-content class=md-padding><div layout=row flex ng-include=\"\'scripts-servicos/pages/servicos/view/adicionarArquivosAjuda.html\'\"></div></md-content></md-tab></md-tabs></md-content><!-- botoes --><div layout=row layout-sm=column layout-align=\"end center\"><md-button class=md-raised ui-sref=servicos.SERVICOS_CONSULTAR_SERVICOS>{{\'btn-retornar\' | translate | uppercase}}</md-button><md-button class=md-raised type=submit>{{\'btn-gravar\' | translate | uppercase}}</md-button></div></div></form></div>");
$templateCache.put("scripts-servicos/pages/servicos/view/consultarServicos.html","<titulo-servico class=texto-principal></titulo-servico><form name=dataTableImovel><md-content flex><div layout=row><md-input-container flex><label>{{\'label-nome-titulo\' | translate}}</label><input ng-model=titulo></md-input-container><md-input-container flex><label>{{\'label-situacao\' | translate}}</label><md-select ng-model=situacao><md-option value=\"\">Selecione</md-option><md-option value=true>Ativo</md-option><md-option value=false>Suspenso</md-option></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=clearFilter()><md-tooltip md-direction=bottom>{{\'toltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate }}</md-button><md-button class=md-raised ng-click=filterFindAll() exibir-elemento-por-permissao=SERVICOS.MANTER_SERVICO,SERVICOS.CONSULTAR_SERVICO><md-tooltip md-direction=bottom>{{\'toltip-pesquiser-servicos\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate }}</md-button><md-button class=md-raised ng-click=novoCadastro(); exibir-elemento-por-permissao=SERVICOS.MANTER_SERVICO><md-tooltip md-direction=bottom>{{\'toltip-adicionar-servicos\' | translate}}</md-tooltip>{{\'btn-novo\' | translate }}</md-button></section></div><!--- fim dos botoes --></md-content></form><div flex style=\"margin-top: 3%\" id=su-servicos><div layout=column ng-show=\"servicos.length > 0\"><md-content flex><md-table-container><table md-table ng-model=vm.imovelSelecionado><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro style=\"width: 565px\">Nome</th><th md-column class=texto-centro>Situa&ccedil;&atilde;o</th><th md-column class=texto-centro style=\"text-align: center !important\">A&ccedil;&otilde;es</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in servicos\"><td md-cell class=texto-centro-corpo><span ng-bind=item.titulo></span></td><td md-cell class=texto-centro-corpo><span ng-bind=\"item.ativo === true ? \'Ativo\' : \'Suspenso\'\"></span></td><td md-cell class=texto-centro-corpo><div layout=row layout-align=\"center center\"><div layout-margin=5><a href=#/servicos/editar/{{item.id}}><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"Editar serviço\"><md-tooltip>Editar servi&ccedil;o</md-tooltip></ng-md-icon></a></div><div layout-margin=5><md-switch ng-model=item.ativo class=md-primary aria-label=\"Switch No Ink\" md-no-ink ng-change=\"toggleAtivo($event, item);\"></md-switch></div><div layout-margin=5><a href=\"\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"Excluir serviço\" exibir-elemento-por-permissao=SERVICOS.EXCLUIR_SERVICO ng-click=\"showConfirmExcluir($event, item.id);\"><md-tooltip>Excluir servi&ccedil;o</md-tooltip></ng-md-icon></a></div></div></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto; background-color: #5bf869\" ng-show=\"servicos.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findPagination md-page-select></md-table-pagination></md-content></div></div>");
$templateCache.put("scripts-servicos/pages/tipo-processo-sei/language/pt-BR.json","{ \"toltip-limpar-campos\": \"Limpar campos\", \"toltip-pesquisar\": \"Pesquisar Tipos de Processo Sei\", \"toltip-adicionar\": \"Adicionar Tipo de Processo Sei\", \"label-id\": \"Identificador\", \"label-codigo\": \"Código\", \"label-descricao\": \"Descrição\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-novo\": \"Novo\", \"label-titulo\": \"Editar\", \"label-identidicador-portal-governo\": \"Identificador no Portal do Governo Federal\", \"label-alerta\": \"Alerta\" }");
$templateCache.put("scripts-servicos/pages/tipo-processo-sei/view/cadastrarTipoProcessoSei.tmpl.html","<div class=standard><titulo-servico dinamico=!(isNew) titulo-statico=tituloStatico class=texto-principal></titulo-servico><form name=form ng-submit=\"validar() && salvar()\"><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-codigo\' | translate}}</label><input ng-model=tipo.codigo md-maxlength=50 maxlength=50 name=\"{{\'label-codigo\' | translate}}\" ng-required=true type=number></md-input-container></div><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-descricao\' | translate}}</label><input ng-model=tipo.descricao md-maxlength=250 maxlength=250 name=\"{{\'label-descricao\' | translate}}\" ng-required=true></md-input-container></div><!-- botoes --><div layout=row layout-sm=column layout-align=\"end center\"><md-button class=md-raised ui-sref=servicos.SERVICOS_CONSULTAR_TIPO_PROCESSO_SEI>{{\'btn-retornar\' | translate | uppercase}}</md-button><md-button class=md-raised type=submit>{{\'btn-gravar\' | translate | uppercase}}</md-button></div></form></div>");
$templateCache.put("scripts-servicos/pages/tipo-processo-sei/view/consultarTipoProcessoSei.html","<titulo-servico class=texto-principal></titulo-servico><form name=dataTable><md-content flex><div layout=row><md-input-container flex><label>{{\'label-codigo\' | translate}}</label><input ng-model=codigo></md-input-container><md-input-container flex><label>{{\'label-descricao\' | translate}}</label><input ng-model=descricao></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=clearFilter()><md-tooltip md-direction=bottom>{{\'toltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate }}</md-button><md-button class=md-raised ng-click=filterFindAll() exibir-elemento-por-permissao=SERVICOS.MANTER_TIPO_PROCESSO_SEI><md-tooltip md-direction=bottom>{{\'toltip-pesquisar\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate }}</md-button><md-button class=md-raised ng-click=novoCadastro(); exibir-elemento-por-permissao=SERVICOS.MANTER_TIPO_PROCESSO_SEI><md-tooltip md-direction=bottom>{{\'toltip-adicionar\' | translate}}</md-tooltip>{{\'btn-novo\' | translate }}</md-button></section></div><!--- fim dos botoes --></md-content></form><div flex style=\"margin-top: 3%\" id=su-tipos-processos-sei><div layout=column ng-show=\"tipos.length > 0\"><md-content flex><md-table-container><table md-table ng-model=vm.tipoSelecionado><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro style=\"width: 565px\">Código</th><th md-column class=texto-centro style=\"width: 565px\">Descrição</th><th md-column class=texto-centro style=\"text-align: center !important\">A&ccedil;&otilde;es</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in tipos\"><td md-cell class=texto-centro-corpo><span ng-bind=item.codigo></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.descricao></span></td><td md-cell class=texto-centro-corpo><div layout=row layout-align=\"center center\"><div layout-margin=5><a href=#/servicos/cadastrartipoprocessosei/{{item.id}}><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"Editar tipo processo SEI\"><md-tooltip>Editar tipo processo SEI</md-tooltip></ng-md-icon></a></div><div layout-margin=5><a href=\"\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"Excluir tipo processo SEI\" exibir-elemento-por-permissao=SERVICOS.MANTER_TIPO_PROCESSO_SEI ng-click=\"showConfirmExcluir($event, item.id);\"><md-tooltip>Excluir tipo processo SEI</md-tooltip></ng-md-icon></a></div></div></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto; background-color: #5bf869\" ng-show=\"tipos.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findPagination md-page-select></md-table-pagination></md-content></div></div>");
$templateCache.put("scripts-servicos/pages/triagem/language/pt-BR.json","{ \"triagem-requerimentos\": \"Triagem de Requerimentos\", \"label-requerimento\": \"Requerimento\", \"nome-servico\": \"Nome do Serviço\", \"label-situacao\": \"Situação\", \"label-nome-requerente\": \"Nome do Interessado\", \"label-cpf-cnpj\": \"CPF/CNPJ do Interessado\", \"label-numero-atendimento\": \"N° do Atendimento\", \"label-periodo\": \"Período\", \"label-num-processo\" : \"N° do Processo\", \"label-requerente\" : \"Interessado\", \"label-atendimento-requerimento\" : \"N° Atendimento\", \"label-situacao\" : \"Situação\", \"label-servico-prioridade\" : \"Serviço com Prioridade no Atendimento\", \"label-anotacoes\" : \"Anotações\", \"label-dt-requerimento\": \"Data do Requerimento\", \"label-dt-prazo-triagem\": \"Data Limite de Triagem\", \"label-dt-prazo-analise-tecnica\": \"Data Limite de Análise Técnica\", \"label-detalhar-requerimento\":\"Detalhar Requerimento\", \"label-acoes\": \"Ações\", \"tooltip-add-anotacao\": \"Adicionar anotação\", \"tooltip-add-cancelar-sei\": \"Aguardando retorno do SEI\", \"tooltip-pendencia\": \"O interessado visualizou as pendências em \", \"analise-tecninca\":\"Ánalise Técnica do Requerimento\", \"label-todos\": \"Todos\", \"label-sim\": \"Sim\", \"label-nao\": \"Não\" }");
$templateCache.put("scripts-servicos/pages/triagem/view/triagemRequerimento.html","<titulo-servico class=texto-principal></titulo-servico><form name=form flex><div layout=row layout-margin=5><md-input-container flex=50><label>{{\'nome-servico\' | translate}}</label><md-select ng-model=filtro.servicoId><md-option ng-selected=true ng-value=undefined>{{\'label-todos\' | translate}}</md-option><md-option ng-repeat=\"serv in servicos\" ng-value=serv.id>{{serv.descricao}}</md-option></md-select></md-input-container><md-input-container flex=50><label>{{\'label-situacao\' | translate}}</label><md-select ng-model=filtro.statusRequerimento ng-change=changeSituacao()><md-option ng-selected=!filtro.statusRequerimento ng-value=undefined>{{\'label-todos\' | translate}}</md-option><md-option ng-repeat=\"(index, value) in situacoes\" value={{index}}>{{value}}</md-option></md-select></md-input-container></div><div layout=row layout-margin=5><md-input-container flex=50><input ng-model=filtro.nomeRequerente aria-label=input><label>{{\'label-nome-requerente\' | translate}}</label></md-input-container><md-input-container flex=50><input ng-model=filtro.cpfCnpj ui-br-cpfcnpj-mask aria-label=input><label>{{\'label-cpf-cnpj\' | translate}}:</label></md-input-container></div><div layout=row layout-margin=5><md-input-container flex=50><input ng-model=filtro.nuAtendimento ui-mask=AA99999/9999 ui-options=\"{allowInvalidValue: true}\" ng-keyup=toUpperCase() ui-mask-placeholder ui-mask-placeholder-char=space model-view-value=true aria-label=input><label>{{\'label-numero-atendimento\' | translate}}</label></md-input-container><md-input-container><label>Pesquisa por Data</label><md-select ng-model=filtro.tipoPesquisaAtendimento ng-change=changeTipo()><md-option selected value=RECEBIDOS_NO_PERIODO>Recebidos no Período</md-option><md-option value=A_VENCER_TRIAGEM>A vencer Triagem</md-option><md-option value=A_VENCER_ANALISE_TECNICA>A vencer Análise Técnica</md-option><md-option value=VENCIDOS_TRIAGEM>Vencidos Triagem</md-option><md-option value=VENCIDOS_ANALISE_TECNICA>Vencidos Análise Técnica</md-option></md-select></md-input-container></div><div layout=row layout-margin=5><md-input-container flex=50><label>Unidade de Atendimento</label><md-select ng-model=filtro.unidadesAtendimento md-on-close=clearSearchItem() data-md-container-class=selectdemoSelectHeader multiple><md-select-header class=demo-select-header><input ng-model=searchItem type=search placeholder=\"Unidade de Atendimento..\" class=\"demo-header-searchbox md-text\"></md-select-header><md-optgroup label=\"Unidade de Atendimento\"><md-option ng-if=usuarioLogado ng-value=item ng-repeat=\"item in usuarioLogado.jurisdicoes |\n              filter:searchItem | orderBy\">{{item}}</md-option></md-optgroup></md-select></md-input-container><md-input layout-margin=2><label>{{\'label-periodo\' | translate}}</label><md-datepicker-custom-servico ng-model=filtro.dataDe md-custom-mask=39/19/9999 md-max-date=\"isVencido() ? filtro.dataAte ? filtro.dataAte : dataAtual : undefined\" md-min-date=\"!isVencido() && !isRecebidos() ? dataAtual : undefined\" md-custom-clean-mask=false md-custom-restrict=reject></md-datepicker-custom-servico><label>a</label><md-datepicker-custom-servico ng-model=filtro.dataAte md-custom-mask=39/19/9999 md-max-date=\"isVencido() ? dataAtual : undefined\" md-min-date=\"!isVencido() && !isRecebidos() ?\n                     filtro.dataDe ? filtro.dataDe : dataAtual\n                     : filtro.dataDe\" name=teste md-custom-clean-mask=true md-custom-restrict=reject></md-datepicker-custom-servico></md-input></div><div layout=row layout-margin=5><md-input-container flex=50><label>{{\'label-prioridade-Atendimento\' | translate}}</label><md-select ng-model=filtro.prioridadeAtendimento><md-option ng-selected=!filtro.prioridadeAtendimento ng-value=undefined>{{\'label-todos\' | translate}}</md-option><md-option value=true>{{\'label-sim\' | translate}}</md-option><md-option value=false>{{\'label-nao\' | translate}}</md-option></md-select></md-input-container><md-input-container flex=50><label>UF do Imóvel</label><md-select ng-model=filtro.ufImovel><md-option ng-selected=true ng-value=undefined>{{\'label-todos\' | translate}}</md-option><md-option ng-repeat=\"uf in listUfs | orderBy\" value={{uf}}>{{uf}}</md-option></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=clearFilter()><md-tooltip md-direction=bottom>{{\'tooltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate}}</md-button><md-button class=md-raised ng-click=filterFindAll() exibir-elemento-por-permissao=\"SERVICOS.CONSULTAR_TRIAGEM, SERVICOS.EFETUAR_ANALISE_TECNICA,\n                                                            SERVICOS.VISUALIZAR_ANALISE_TECNICA, SERVICOS.CONCLUIR_ANALISE\"><md-tooltip md-direction=bottom>{{\'tooltip-pesquisar\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate}}</md-button></section></div><!--- fim dos botoes --></form><div layout=column ng-show=\"consultaTriagem.length > 0\"><md-content><md-table-container><table md-table><thead md-head md-order=nomeRequerente><tr md-row><th md-column>{{\'label-num-processo\' | translate}}</th><!--<th md-column md-order-by=\"ufImovel\">UF do Atendimento</th>--><th md-column ng-click=\"sort(\'ufDoAtendimento\')\">UF do Atendimento <i class=material-icons ng-if=ufDoAtendimento>{{ufDoAtendimento ? \'keyboard_arrow_down\' : \'\'}}</i> <i class=material-icons ng-if=!ufDoAtendimento>{{ isSortDown(\'ufDoAtendimento\') ? \'keyboard_arrow_down\' : \'\'}} {{isSortUp(\'ufDoAtendimento\') ? \'keyboard_arrow_up\' : \'\'}}</i></th><th md-column ng-click=\"sort(\'interessado\')\">Interessado <i class=material-icons ng-if=interessado>{{interessado ? \'keyboard_arrow_down\' : \'\'}}</i> <i class=material-icons ng-if=!interessado>{{ isSortDown(\'interessado\') ? \'keyboard_arrow_down\' : \'\'}} {{isSortUp(\'interessado\') ? \'keyboard_arrow_up\' : \'\'}}</i></th><!--<th md-column md-order-by=\"nomeRequerente\">{{\'label-requerente\' |--><!--translate}}--><!--</th>--><th md-column ng-click=\"sort(\'nomeDoServico\')\">Nome do Serviço <i class=material-icons ng-if=nomeDoServico>{{nomeDoServico ? \'keyboard_arrow_down\' : \'\'}}</i> <i class=material-icons ng-if=!nomeDoServico>{{ isSortDown(\'nomeDoServico\') ? \'keyboard_arrow_down\' : \'\'}} {{isSortUp(\'nomeDoServico\') ? \'keyboard_arrow_up\' : \'\'}}</i></th><!--<th md-column md-order-by=\"nomeServico\">--><!--Nome do Serviço--><!--</th>--><th md-column ng-click=\"sort(\'numeroDoAtendimento\')\">N° Atendimento <i class=material-icons ng-if=numeroDoAtendimento>{{numeroDoAtendimento ? \'keyboard_arrow_down\' : \'\'}}</i> <i class=material-icons ng-if=!numeroDoAtendimento>{{ isSortDown(\'numeroDoAtendimento\') ? \'keyboard_arrow_down\' : \'\'}} {{isSortUp(\'numeroDoAtendimento\') ? \'keyboard_arrow_up\' : \'\'}}</i></th><!--<th md-column md-order-by=\"numeroAtendimento\">--><!--{{\'label-atendimento-requerimento\' | translate}}--><!--</th>--><th md-column>{{\'label-situacao\' | translate}}</th><th md-column>{{\'label-dt-prazo-triagem\' | translate}}</th><th md-column>{{\'label-dt-prazo-analise-tecnica\' | translate}}</th><th md-column ng-click=\"sort(\'dataDoRequerimento\')\">Data do Requerimento <i class=material-icons ng-if=dataDoRequerimento>{{dataDoRequerimento ? \'keyboard_arrow_down\' : \'\'}}</i> <i class=material-icons ng-if=!dataDoRequerimento>{{ isSortDown(\'dataDoRequerimento\') ? \'keyboard_arrow_down\' : \'\'}} {{isSortUp(\'dataDoRequerimento\') ? \'keyboard_arrow_up\' : \'\'}}</i></th><!--<th md-column md-order-by=\"dataEnvio\">{{\'label-dt-requerimento\' |--><!--translate}}--><!--</th>--><th md-column>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in consultaTriagem | orderBy: nomeRequerente\" ng-class=\"{\'destaque-cinza\': item.statusRequerimento == \'Requerimento Recebido\',\'destaque-azul\':item.habilitarPrioridadeAtendimento}\"><td md-cell><a href=\"https://protocolointegrado.gov.br/Protocolo/documento/listar_documentos.jsf?campoBuscaSimples={{item.procedimentoSomenteDigito}}\" target=_blank><span ng-bind=item.procedimentoFormatado></span></a></td><td md-cell><span ng-bind=item.ufImovel></span></td><td md-cell><span ng-bind=item.nomeRequerente></span></td><td md-cell><span ng-bind=item.nomeServico></span></td><td md-cell><span ng-bind=item.numeroAtendimento></span></td><td md-cell><span ng-bind=item.statusRequerimento></span><ng-md-icon ng-show=item.pendenciaVisualizada icon=check_circle style=fill:#788489 size=22 alt=\"\"><md-tooltip>{{\'tooltip-pendencia\' | translate}} {{item.dataPendenciaVisualizada}}</md-tooltip></ng-md-icon><ng-md-icon ng-show=item.novaInformacao icon=error style=fill:#788489 size=22 alt=\"\"><md-tooltip>Novo documento/informação inserido pelo requerente.</md-tooltip></ng-md-icon></td><td md-cell><span ng-bind=formatarData(item.dataPrazoTriagem)></span></td><td md-cell><span ng-bind=formatarData(item.dataPrazoAnaliseTecnica)></span></td><td md-cell><span ng-bind=formatarData(item.dataEnvio)></span></td><td md-cell class=speed-dial><md-fab-speed-dial md-direction=left class=\"hand md-scale\"><md-fab-trigger><ng-md-icon class=icon-acao icon=more_vert></ng-md-icon></md-fab-trigger><md-fab-actions><span layout=row layout-align=\"left center\"><ng-md-icon ng-show=item.habilitarPrioridadeAtendimento class=hand icon=warning style=fill:#788489 size=22 alt=\"\"><md-tooltip>{{\'icon-prioridade-atendimento\' | translate}}</md-tooltip></ng-md-icon><a style=\"text-decoration: none\" ng-click=irParaDetalhar(item); exibir-elemento-por-permissao=\"SERVICOS.EFETUAR_ANALISE_TECNICA,\n                                                      SERVICOS.VISUALIZAR_ANALISE_TECNICA, SERVICOS.CONCLUIR_ANALISE\" id=detalharRequerimento><ng-md-icon class=hand icon=search style=fill:#788489 size=22 alt=\"\"><md-tooltip>{{\'label-detalhar-requerimento\' | translate}}</md-tooltip></ng-md-icon></a><ng-md-icon class=hand icon=rate_review style=fill:#788489 size=22 alt=\"{{\'tooltip-add-anotacao\' | translate}}\" ng-click=abrirModalAnotacoes(item)><md-tooltip>{{\'tooltip-add-anotacao\' | translate}}</md-tooltip></ng-md-icon><md-icon ng-if=\"item.statusRequerimento == \'Aguardando retorno do SEI\'\" md-svg-src=assets/img/icone-sei.svg class=hand icon=rate_review style=fill:#788489 size=22 alt=\"{{\'tooltip-add-anotacao\' | translate}}\" ng-click=abrirModalCancelarEnvioAutomaticoSei(item)><md-tooltip>{{\'tooltip-add-cancelar-sei\' | translate}}</md-tooltip></md-icon><span ng-if=\"(item.dataPrazoTriagem != null || item.dataPrazoAnaliseTecnica != null) && !item.isPausado\"><ng-md-icon exibir-elemento-por-permissao=SERVICOS.INTERROMPER_CONTINUAR_ATENDIMENTO class=hand icon=pause style=fill:#788489 size=22 alt=\"Interromper Atendimento\" exibir-elemento-por-permissao=SERVICO.INTERROMPER_CONTINUAR_ATENDIMENTO ng-click=pausarAtendimento(item)><md-tooltip>Interromper Atendimento</md-tooltip></ng-md-icon></span><span ng-if=\"(item.dataPrazoTriagem != null || item.dataPrazoAnaliseTecnica != null) && item.isPausado\"><ng-md-icon exibir-elemento-por-permissao=SERVICOS.INTERROMPER_CONTINUAR_ATENDIMENTO class=hand icon=play_arrow style=fill:#788489 size=22 alt=\"Continuar Atendimento\" ng-click=despausarAtendimento(item)><md-tooltip>Continuar Atendimento</md-tooltip></ng-md-icon></span></span></md-fab-actions></md-fab-speed-dial></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto; background-color: #D8F8E1\" ng-show=\"consultaTriagem.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=navegarPaginacao md-page-select></md-table-pagination></md-content></div>");
$templateCache.put("scripts-servicos/pages/unidade-sei/language/pt-BR.json","{ \"toltip-limpar-campos\": \"Limpar campos\", \"toltip-pesquisar-unidades\": \"Pesquisar Unidades Sei\", \"toltip-adicionar-unidades\": \"Adicionar Unidade Sei\", \"label-servico\": \"Serviço\", \"label-id\": \"Identificador\", \"label-codigo\": \"Código da Unidade SEI\", \"label-sigla\": \"Sigla da Unidade SEI\", \"label-uf\": \"UF\", \"label-descricao\": \"Descrição da Unidade SEI\", \"label-email\": \"E-mail de recebimento de log de erro\", \"label-grupo\": \"Grupo Processo SEI\", \"label-tipo-processo\": \"Tipo Processo SEI\", \"label-todos\": \"Todos\", \"label-unidade-central\": \"Unidade Central\", \"btn-limpar\": \"Limpar\", \"btn-pesquisar\": \"Pesquisar\", \"btn-novo\": \"Novo\", \"btn-nova-descricao\":\"Nova Descrição\", \"label-titulo\": \"Editar\", \"label-titulo-detalhar\": \"Unidade SEI\", \"label-nome-titulo\": \"Nome/Título\", \"label-identidicador-portal-governo\": \"Identificador no Portal do Governo Federal\", \"label-alerta\": \"Alerta\" }");
$templateCache.put("scripts-servicos/pages/unidade-sei/view/cadastrarUnidadeSei.tmpl.html","<div class=standard><titulo-servico dinamico=!(isNew) titulo-statico=tituloStatico class=texto-principal></titulo-servico><form name=form ng-submit=\"validar() && salvar()\"><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-codigo\' | translate}}</label><input ng-model=unidade.codigoSei md-maxlength=50 maxlength=50 name=\"{{\'label-codigo\' | translate}}\" ng-required=true type=number></md-input-container><md-input-container flex><label>{{\'label-sigla\' | translate}}</label><input ng-model=unidade.sigla md-maxlength=50 maxlength=50 name=\"{{\'label-sigla\' | translate}}\" ng-required=true></md-input-container></div><!--div layout=\"row\" layout-margin=\"5\">\n      <md-input-container flex ng-if=\"!novaDescricao\">\n        <md-select ng-model=\"unidade.descricao\" aria-label=\"Descrição\" required=\"true\">\n          <md-option ng-selected=\"true\" ng-value=\"undefined\" >{{\'label-descricao\' | translate}} *</md-option>          \n          <md-option ng-repeat=\"desc in listaDescricoes | orderBy\" value=\"{{desc}}\">{{desc}}</md-option>\n        </md-select>  \n      </md-input-container> \n      <md-input-container flex ng-if=\"novaDescricao\">\n        <label>{{\'label-descricao\' | translate}}</label>\n        <input ng-model=\"unidade.descricao\" class=\"lista-editavel\"\n                md-maxlength=\"250\"\n                maxlength=\"250\"\n                name=\"{{\'label-descricao\' | translate}}\"\n                ng-required=\"true\">     \n      </md-input-container>    \n      <md-button class=\"md-raised\" ng-click=\"cadastrarDescricao()\" ng-if=\"!novaDescricao\">\n        {{\'btn-nova-descricao\' | translate | uppercase}}\n    </md-button> \n   </div--><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-descricao\' | translate}}</label><md-autocomplete md-no-cache=true md-selected-item=unidade.descricao md-items=\"desc in filtraUnidade(filtro)\" md-search-text=filtro md-item-text=desc md-min-length=0><span>{{ desc }}</span></md-autocomplete></md-input-container></div><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-email\' | translate}}</label><input ng-model=unidade.email md-maxlength=100 maxlength=100 name=\"{{\'label-email\' | translate}}\" ng-required=true></md-input-container></div><div layout=row layout-margin=5><md-input-container flex><md-select ng-model=unidade.uf aria-label=UF required><md-option ng-selected=true ng-value=undefined>{{\'label-uf\' | translate}}</md-option><md-option ng-repeat=\"uf in listUfs | orderBy\" value={{uf}}>{{uf}}</md-option></md-select></md-input-container><md-input-container flex><md-select ng-model=unidade.idGrupoUnidadeSei aria-label=\"Grupo Processo SEI\" required><md-option ng-selected=true ng-value=undefined>{{\'label-grupo\' | translate}}</md-option><md-option ng-repeat=\"grupo in listGrupos | orderBy\" value={{grupo.id}}>{{grupo.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-margin=5><md-input-container flex><labe>{{\'label-unidade-central\' | translate}}</labe><md-radio-group class=md-primary ng-model=unidade.unidadeCentral layout=row name=unidadeCentral><md-radio-button ng-value=true>{{\'rb-sim\' | translate}}</md-radio-button><md-radio-button ng-value=false>{{\'rb-nao\' | translate}}</md-radio-button></md-radio-group></md-input-container></div><!-- botoes --><div layout=row layout-sm=column layout-align=\"end center\"><md-button class=md-raised ui-sref=servicos.SERVICOS_CONSULTAR_UNIDADE_SEI>{{\'btn-retornar\' | translate | uppercase}}</md-button><md-button class=md-raised type=submit>{{\'btn-gravar\' | translate | uppercase}}</md-button></div></form></div>");
$templateCache.put("scripts-servicos/pages/unidade-sei/view/consultarUnidadeSei.html","<titulo-servico class=texto-principal></titulo-servico><form name=dataTable><md-content flex><div layout=row><md-input-container flex><label>{{\'label-codigo\' | translate}}</label><input ng-model=codigoSei></md-input-container><md-input-container flex><label>{{\'label-sigla\' | translate}}</label><input ng-model=sigla></md-input-container><md-input-container flex><label>{{\'label-uf\' | translate}}</label><md-select ng-model=uf aria-label=UF><md-option ng-selected=true ng-value=undefined>{{\'label-todos\' | translate}}</md-option><md-option ng-repeat=\"ufa in listUfs | orderBy\" value={{ufa}}>{{ufa}}</md-option></md-select></md-input-container></div><div layout=row><md-input-container flex><md-select ng-model=descricao aria-label=Descrição><md-option ng-selected=true ng-value=undefined>{{\'label-descricao\' | translate}}</md-option><md-option ng-repeat=\"desc in listaDescricoes | orderBy\" value={{desc}}>{{desc}}</md-option></md-select></md-input-container></div><!--- Inicio dos botoes --><div layout=row layout-align=\"end right\"><section layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised ng-click=clearFilter()><md-tooltip md-direction=bottom>{{\'toltip-limpar-campos\' | translate}}</md-tooltip>{{\'btn-limpar\' | translate }}</md-button><md-button class=md-raised ng-click=filterFindAll() exibir-elemento-por-permissao=SERVICOS.MANTER_UNIDADE_SEI><md-tooltip md-direction=bottom>{{\'toltip-pesquisar-unidades\' | translate}}</md-tooltip>{{\'btn-pesquisar\' | translate }}</md-button><md-button class=md-raised ng-click=novoCadastro(); exibir-elemento-por-permissao=SERVICOS.MANTER_UNIDADE_SEI><md-tooltip md-direction=bottom>{{\'toltip-adicionar-unidades\' | translate}}</md-tooltip>{{\'btn-novo\' | translate }}</md-button></section></div><!--- fim dos botoes --></md-content></form><div flex style=\"margin-top: 3%\" id=su-unidades><div layout=column ng-show=\"unidades.length > 0\"><md-content flex><md-table-container><table md-table ng-model=vm.unidadeSelecionada><thead md-head><tr md-row style=\"color: #fff; font-size: 12pt\"><th md-column class=texto-centro style=\"width: 565px\">Código da Unidade SEI</th><th md-column class=texto-centro style=\"width: 565px\">Sigla da Unidade SEI</th><th md-column class=texto-centro style=\"width: 565px\">Descrição da Unidade SEI</th><th md-column class=texto-centro style=\"width: 565px\">UF</th><th md-column class=texto-centro style=\"width: 565px\">Grupo de Processo SEI</th><th md-column class=texto-centro style=\"text-align: center !important\">A&ccedil;&otilde;es</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in unidades\"><td md-cell class=texto-centro-corpo><span ng-bind=item.codigoSei></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.sigla></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.descricao></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.uf></span></td><td md-cell class=texto-centro-corpo><span ng-bind=item.grupoUnidadeDesc></span></td><td md-cell class=texto-centro-corpo><div layout=row layout-align=\"center center\"><div layout-margin=5><a href=#/servicos/cadastrarunidadesei/{{item.idUnidade}}><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"Editar unidade SEI\"><md-tooltip>Editar unidade SEI</md-tooltip></ng-md-icon></a></div><div layout-margin=5><a style=\"text-decoration: none\" href=#/servicos/detalharunidadesei/{{item.idUnidade}} id=detalharUnidadeSei><ng-md-icon class=hand icon=search style=fill:#788489 size=22 alt=\"\"><md-tooltip>Detalhar Unidade SEI</md-tooltip></ng-md-icon></a></div><div layout-margin=5><a href=\"\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"Excluir unidade SEI\" exibir-elemento-por-permissao=SERVICOS.MANTER_UNIDADE_SEI ng-click=\"showConfirmExcluir($event, item.idUnidade);\"><md-tooltip>Excluir unidade SEI</md-tooltip></ng-md-icon></a></div></div></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto; background-color: #5bf869\" ng-show=\"unidades.length > 0\" md-limit=tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=registroInicial md-total={{totalItems}} md-on-paginate=findPagination md-page-select></md-table-pagination></md-content></div></div>");
$templateCache.put("scripts-servicos/pages/unidade-sei/view/detalharUnidadeSei.html","<div class=standard><titulo-servico dinamico=!(isNew) titulo-statico=tituloStatico class=texto-principal></titulo-servico><form name=form ng-submit=\"validar() && salvar()\"><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-codigo\' | translate}}</label><input ng-model=unidade.codigoSei disabled></md-input-container><md-input-container flex><label>{{\'label-sigla\' | translate}}</label><input ng-model=unidade.sigla disabled></md-input-container></div><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-descricao\' | translate}}</label><input ng-model=unidade.descricao disabled></md-input-container></div><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-email\' | translate}}</label><input ng-model=unidade.email disabled></md-input-container></div><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-uf\' | translate}}</label><md-select ng-model=unidade.uf aria-label=UF disabled><md-option ng-repeat=\"uf in listUfs | orderBy\" value={{uf}}>{{uf}}</md-option></md-select></md-input-container><md-input-container flex><label>{{\'label-grupo\' | translate}}</label><md-select ng-model=unidade.idGrupoUnidadeSei aria-label=\"Grupo Processo SEI\" disabled><md-option ng-repeat=\"grupo in listGrupos | orderBy\" value={{grupo.id}}>{{grupo.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-margin=5><md-input-container flex><label>{{\'label-unidade-central\' | translate}}</label><br><p>{{unidade.unidadeCentral ? \'Sim\' : \'Não\'}}</p></md-input-container></div><!-- botoes --><div layout=row layout-sm=column layout-align=\"end center\"><md-button class=md-raised ui-sref=servicos.SERVICOS_CONSULTAR_UNIDADE_SEI>{{\'btn-retornar\' | translate | uppercase}}</md-button></div></form></div>");
$templateCache.put("scripts-servicos/app/directives/footer/templates/acoesRequerimentoServico.html","<md-content ng-if=\"requerimento.statusRequerimento &&\n                     requerimento.statusRequerimento == AGUARDANDO_ANALISE_PREVIA\"><div layout=row layout-align=\"end end\" layout-align-xs=\"center center\"><md-button class=\"md-primary md-raised\" ng-click=fechar() ng-model=cancelar><label>{{\'btn-fechar\' | translate}}</label></md-button><md-button class=\"md-primary md-raised\" ng-model=salvar ng-click=iniciarAnalise() exibir-elemento-por-permissao=SERVICOS.EFETUAR_ANALISE_TECNICA id=iniciarTriagem><label>{{\'btn-inicializar-triagem\' | translate}}</label></md-button></div></md-content><md-content ng-if=\"requerimento.statusRequerimento &&\n                    requerimento.statusRequerimento != AGUARDANDO_ANALISE_PREVIA\" exibir-elemento-por-permissao=\"SERVICOS.EFETUAR_ANALISE_TECNICA,SERVICOS.CONCLUIR_ANALISE \"><analise-simplificada-servicos requerimento=requerimento show-analise=\"requerimento.statusRequerimento != AGUARDANDO_ANALISE_PREVIA &&\n                          requerimento.statusRequerimento\"></analise-simplificada-servicos></md-content>");
$templateCache.put("scripts-servicos/app/directives/utilitarios/templates/adicionarChecklist.html","<md-content flex id=su-servicos><div layout=row layout-sm=column layout-align=end layout-wrap><md-button class=md-raised style=\"margin-left: 0\" ng-click=modalCheckList()>{{\'btn-adicionar\' | translate}}<md-icon class=\"material-icons md-18\" style=\"color: white\">add_circle_outline</md-icon></md-button></div><div layout=row ng-show=\"listaChecklist.length > 0\" class=top-1><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-item\' | translate}}</th><th md-column class=texto-centro>{{\'label-obrigatorio\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in listaChecklist | orderBy: \'ordem\' | limitTo: queryChecklist.limit: (queryChecklist.page - 1) * queryChecklist.limit\"><td md-cell class=texto-centro-corpo><div ng-bind-html=\"item.item | to_trusted\"></div></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.obrigatorio ? \'sim\' : \'nao\' | translate}}</p></td><td md-cell class=texto-centro-corpo><ng-md-icon icon=delete style=fill:#788489 size=22 alt={{labelExcluirItemChecklist}} ng-click=\"removerChecklist($event, item);\"><md-tooltip>{{labelExcluirItemChecklist}}</md-tooltip></ng-md-icon></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listaChecklist.length > 0\" ng-show=\"listaChecklist.length > 0\" md-limit=queryChecklist.limit md-limit-options=queryChecklist.limitsPage md-page=queryChecklist.page md-total={{listaChecklist.length}} md-page-select=true></md-table-pagination></div></div></md-content>");
$templateCache.put("scripts-servicos/app/directives/utilitarios/templates/analiseTecnicaSimplificada.html","<fieldset><div layout=row flex=100 layout-align=\"center center\"><h2>{{\'triagem\' | translate | uppercase}}</h2></div><div layout=column ng-init=init(); style=\"margin-bottom: 10px\" ng-if=\"historicos.length > 0\"><div ng-if=\"requerimento.prioridadeAtendimento.prioridadeBetween || requerimento.prioridadeAtendimento.prioridadeAcima || requerimento.prioridadeAtendimento.prioridadeDoenca\"><div><b>Esse serviço foi definido como prioritário pelo interessado. Confirma Priorização?</b><md-radio-group class=\"md-primary confirma-priorizacao\" ng-model=requerimento.prioridadeAtendimento.confirmaPriorizacao ng-disabled=!isTriagem() flex-offset=5 flex-offset-xs=5 layout=row ng-required=isCampoObrigatorio() name=\"{{\'label-analise-tecnica\' | translate}}:  analise-{{$index}}\"><md-radio-button ng-value=true ng-disabled=bloquearFormulario>{{\'rb-sim\' | translate}}</md-radio-button><md-radio-button ng-value=false ng-disabled=bloquearFormulario>{{\'rb-nao\' | translate}}</md-radio-button></md-radio-group><div ng-if=\"requerimento.prioridadeAtendimento.confirmaPriorizacao == false\" class=marginTop15><md-input-container class=md-block><label>{{\'label-justificativa\' | translate}}</label><input rows=5 ng-disabled=!isTriagem() ng-model=requerimento.prioridadeAtendimento.justificativa name=\"{{\'label-justificativa\' | translate}}\" aria-label=input md-maxlength=300 required maxlength=300></md-input-container></div></div></div><v-accordion id=accordion class=vAccordion--default flex=100><v-pane expanded id=historico><v-pane-header class=texto-verde><h3>{{\'label-historico\' | translate | uppercase}}</h3></v-pane-header><v-pane-content><div layout=row layout-xs=column><div layout=column flex=100><md-content flex=100><md-table-container><table md-table><thead md-head><tr md-row><th md-column>{{\'label-situacao\' | translate}}</th><th md-column>{{\'label-data\' | translate}}</th><th md-column>{{\'label-responsavel\' | translate}}</th><th md-column>{{\'label-justificativa-anotacao\' | translate}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"historico in historicos\"><td md-cell>{{historico.situacaoAtual}}</td><td md-cell>{{historico.dataAlteracao | date: \'dd/MM/yyyy\'}}</td><td md-cell>{{historico.nomeResponsavel}}</td><td md-cell class=historico-requerimento><!--<div ng-if=\"historico.situacaoAtual == \'Retorno de Pendência\'\">--><!--<input type=\"checkbox\" name=\"justificativa\" id=\"{{historico.id}}\" />--><!--<div ng-class=\"{\'historico-justificativa-list\': checkField(historico)}\" >--><!--<div ng-bind-html=\"historico.justificativa\"></div>--><!--</div>--><!--<label ng-if=\"checkField(historico)\" for=\"{{historico.id}}\" class=\"justificativa-label\"></label>--><!--</div>--><div ng-if=\"historico.situacaoAtual != \'Retorno de Pendência\'\" read-more content={{historico.justificativa}} limit=180 more-text=mais... less-text=menos...></div></td></tr></tbody></table></md-table-container><md-table-pagination style=\"margin: auto; background-color: #D8F8E1\" ng-show=\"historicos.length > 0\" md-limit=ctrl.tamanhoLimite md-limit-options=\"[5, 10, 15]\" md-page=ctrl.registroInicial md-total={{totalItems}} md-on-paginate=buscarHistorico md-page-select></md-table-pagination></md-content></div></div></v-pane-content></v-pane></v-accordion></div><form name=form ng-submit=\"validarFormulario(form) && salvar()\" novalidate><div layout=column layout-xs=column><!--<h3 ng-if=\"isStatusEmAnalisePrevia()\">{{\'label-analise-previa-check-list\' | translate | uppercase}}</h3>--><h3 ng-if=true>{{\'label-analise-previa-check-list\' | translate | uppercase}}</h3><!--<h3 ng-if=\"isStatusEmAnaliseTecnica()\">{{\'label-analise-tecnica-check-list\' | translate | uppercase}}</h3>--><!--<h3 ng-if=\"false\">{{\'label-analise-tecnica-check-list\' | translate | uppercase}}</h3>--><fieldset><legend><h3>{{\'label-atesto-devidos-fins\' | translate}}</h3></legend><md-list><!--<md-list-item ng-if=\"isStatusEmAnalisePrevia()\" ng-repeat=\"analise in analiseRequerimento.analiseChecklist\"--><!--class=\"noright\">--><md-list-item ng-if=true ng-repeat=\"analise in analiseRequerimento.analiseChecklist\" class=noright><p></p><div ng-bind-html=analise.analiseTriagem.item></div><p></p><md-radio-group class=md-primary ng-model=analise.resposta ng-change=verificarResposta(analise) flex-offset=5 flex-offset-xs=5 layout=row layout-align=\"center center\" ng-required=isCampoObrigatorio() name=\"{{\'label-analise-tecnica\' | translate}}:  analise-{{$index}}\"><md-radio-button ng-value=true ng-disabled=bloquearFormulario>{{\'rb-sim\' | translate}}</md-radio-button><md-radio-button ng-value=false ng-disabled=bloquearFormulario>{{\'rb-nao\' | translate}}</md-radio-button></md-radio-group><md-icon class=hand flex-offset-xs=5 flex-offset=5 ng-click=abrirModalJustificativa(analise) ng-if=!bloquearFormulario>edit</md-icon></md-list-item><!--<md-list-item ng-if=\"isStatusEmAnaliseTecnica()\" ng-repeat=\"analise in analiseRequerimento.analiseTecnicaChecklist\"--><!--class=\"noright\">--><md-list-item ng-if=false ng-repeat=\"analise in analiseRequerimento.analiseTecnicaChecklist\" class=noright><p></p><div ng-bind-html=analise.analiseTecnica.item></div><p></p><md-radio-group class=md-primary ng-model=analise.resposta ng-change=verificarResposta(analise) flex-offset=5 flex-offset-xs=5 layout=row layout-align=\"center center\" ng-required=isCampoObrigatorio() name=\"{{\'label-analise-tecnica\' | translate}}:  analise-{{$index}}\"><md-radio-button ng-value=true ng-disabled=bloquearFormulario>{{\'rb-sim\' | translate}}</md-radio-button><md-radio-button ng-value=false ng-disabled=bloquearFormulario>{{\'rb-nao\' | translate}}</md-radio-button></md-radio-group><md-icon class=hand flex-offset-xs=5 flex-offset=5 ng-click=abrirModalJustificativa(analise) ng-if=!bloquearFormulario>edit</md-icon></md-list-item></md-list></fieldset><h3>{{\'label-documento-adicionados-requerimento\' | translate}}</h3><fieldset><div><legend ng-show=existTipoDoc.OBRIGATORIO><h3>{{\'label-obrigatorios\' | translate | uppercase}}</h3></legend><md-list ng-show=existTipoDoc.OBRIGATORIO><md-list-item ng-repeat=\"doc in analiseRequerimento.analiseDocumentoChecklist\" class=noright ng-if=\"doc.documento.tipo == \'OBRIGATORIO\'\"><p>{{doc.documento.nome}}</p><md-icon class=hand modal-documento=doc.documento modal-id-requerimento=requerimento.id flex-offset-xs=5 flex-offset=5>description</md-icon><md-radio-group class=md-primary ng-model=doc.resposta flex-offset-xs=5 ng-change=verificarResposta(doc) flex-offset=5 layout=row layout-align=\"center center\" name=\"{{\'label-analise-tecnica\' | translate}}:  documentos-{{$index}}\" ng-required=isCampoObrigatorio()><md-radio-button ng-value=true ng-disabled=bloquearFormulario>{{\'rb-sim\' | translate}}</md-radio-button><md-radio-button ng-value=false ng-disabled=bloquearFormulario>{{\'rb-nao\' | translate}}</md-radio-button></md-radio-group><md-icon class=hand flex-offset-xs=5 flex-offset=5 ng-click=abrirModalJustificativa(doc) ng-if=!bloquearFormulario>edit</md-icon></md-list-item></md-list><legend ng-show=existTipoDoc.COMPLEMENTAR><h3>{{\'label-complementares\' | translate |uppercase}}</h3></legend><md-list ng-show=existTipoDoc.COMPLEMENTAR><md-list-item ng-repeat=\"docComp in analiseRequerimento.analiseDocumentoChecklist\" class=noright ng-if=\"docComp.documento.tipo == \'COMPLEMENTAR\'\"><p>{{docComp.documento.nome}}</p><md-icon class=hand modal-documento=docComp.documento modal-id-requerimento=requerimento.id flex-offset-xs=5 flex-offset=5>description</md-icon><md-radio-group class=md-primary ng-model=docComp.resposta flex-offset-xs=5 flex-offset=5 ng-change=verificarResposta(docComp) layout=row layout-align=\"center center\" name=\"{{\'Documento: \' + docComp.documento.nome}}\" ng-required=isCampoObrigatorio()><md-radio-button ng-value=true ng-disabled=bloquearFormulario>{{\'rb-sim\' | translate}}</md-radio-button><md-radio-button ng-value=false ng-disabled=bloquearFormulario>{{\'rb-nao\' | translate}}</md-radio-button></md-radio-group><md-icon class=hand flex-offset-xs=5 flex-offset=5 ng-click=abrirModalJustificativa(docComp) ng-if=!bloquearFormulario>edit</md-icon></md-list-item></md-list></div></fieldset><div layout=row layout-margin=15><!-- Adicionar Permissoes --><md-radio-group class=md-primary ng-model=analiseRequerimento.statusRequerimento layout=column ng-required=true ng-change=alterarStatus() name=\"{{\'status-requerimento\' | translate}}\"><div ng-if=\"!bloquearFormulario && !concluirAtendimento && apresentarProcessoSEI()\"><md-radio-button value=\"{{requerimento.servico.conclusaoServicoTriagem == null ? STATUS_EM_ANALISE_TECNICA : STATUS_CONCLUIDO_TRIAGEM}}\" ng-show=autuarProcesso ng-disabled=bloquearFormulario id=autuarProcesso>{{requerimento.servico.conclusaoServicoTriagem == null ? \'rb-autuar-processo\' : \'rb-concluir-processo\' | translate}}</md-radio-button><div flex-offset-xs=5 ng-show=\"(!bloquearFormulario && !concluirAtendimento && (analiseRequerimento.statusRequerimento == STATUS_EM_ANALISE_TECNICA || requerimento.servico.conclusaoServicoTriagem == true))\" flex-offset=5 layout=column><md-radio-group ng-model=analiseRequerimento.atuacaoProcesso layout=row flex-offset-xs=5 style=\"margin-bottom: 8px\" flex-offset=5 ng-required=\"analiseRequerimento.statusRequerimento == STATUS_EM_ANALISE_TECNICA\" name=\"{{\'msg-autuacao-processo\' | translate}}\"><md-radio-button value={{STATUS_PROCESSO_EXISTENTE}}>{{\'rb-processo-existente\' | translate}}</md-radio-button><md-radio-button ng-click=\"analiseRequerimento.numeroProcesso = undefined\" value={{STATUS_NOVO_PROCESSO}} id=novoProcesso>{{\'rb-criar-novo-processo\' | translate}}</md-radio-button></md-radio-group><md-input-container class=md-block ng-show=\"analiseRequerimento.atuacaoProcesso == STATUS_PROCESSO_EXISTENTE\"><label>{{\'label-numero-processo\' | translate}}</label><input rows=5 ng-model=analiseRequerimento.numeroProcesso ui-mask-placeholder ui-mask-placeholder-char=space name=\"{{\'msg-num-processo-existente\' | translate}}\" model-view-value=true aria-label=input ng-required=\"analiseRequerimento.statusRequerimento == STATUS_EM_ANALISE_TECNICA\n                                                        && analiseRequerimento.atuacaoProcesso == STATUS_PROCESSO_EXISTENTE\"></md-input-container></div></div><md-radio-button ng-show=\"bloquearFormulario || concluirAtendimento\" ng-disabled=bloquearFormulario value={{STATUS_DEFERIDO}}>{{\'rb-concluir-deferimento\' | translate}}</md-radio-button><md-radio-button ng-show=\"bloquearFormulario || concluirAtendimento\" ng-disabled=bloquearFormulario value={{STATUS_INDEFERIDO}}>{{\'rb-concluir-indeferimento\' | translate}}</md-radio-button><md-radio-button ng-show=\"(bloquearFormulario || concluirAtendimento) && requerimento.servico.conclusaoServicoTriagem != null \" ng-disabled=bloquearFormulario value={{STATUS_CONCLUIDO_TRIAGEM}}>{{\'rb-concluir-na-triagem\' | translate}}</md-radio-button><md-radio-button ng-disabled=bloquearFormulario value={{STATUS_AGUARDANDO_REQUERENTE}}>{{\'rb-retornar-pendencia\' | translate}}</md-radio-button><md-radio-button ng-disabled=bloquearFormulario value={{STATUS_CANCELADO}}>{{\'rb-cancelar-requerimento\' | translate}}</md-radio-button></md-radio-group></div><!-- Fim radio --><div layout=row><md-input-container class=md-block flex><label>{{\'label-anotacoes-complementares\' | translate}}</label><textarea ng-model=analiseRequerimento.justificativa maxlength=500 md-maxlength=500 ng-disabled=bloquearFormulario name=\"{{\'label-name-anotacoes-complementares\' | translate}}\" ng-required=statusRequerimentoObrigatorio()>\n                    </textarea></md-input-container></div><!-- <div layout=\"row\">\n                <documentos-concluir-atendimento flex=\"100\"\n                            doc-concluir-atendimento=\"docConcluirAtendimento\">\n                </documentos-concluir-atendimento>\n            </div> --><div layout=row ng-if=\"bloquearFormulario || concluirAtendimento\"><arquivos-concluir-atendimento arquivos=analiseRequerimento.arquivos bloquear-formulario=bloquearFormulario></arquivos-concluir-atendimento></div><div layout=row layout-align=\"end center\"><md-button class=md-raised ui-sref=servicos.SERVICOS_TRIAGEM>{{\'btn-fechar\' | translate}}</md-button><md-button class=md-raised id=botaoEnviar type=submit ng-if=!bloquearFormulario ng-disabled=enviou exibir-elemento-por-permissao=SERVICOS.CONCLUIR_ANALISE,SERVICOS.EFETUAR_ANALISE_TECNICA>{{\'btn-enviar\' | translate}}</md-button></div></div></form></fieldset>");
$templateCache.put("scripts-servicos/app/directives/utilitarios/templates/arquivosConcluirAtendimento.html","<div layout=row><label>{{\'label-arquivos-do-documento\' | translate}}:</label></div><div layout=row layout-xs=column style=\"margin-top: 1%\"><md-input-container class=md-block flex=50 flex-gt-xs style=\"margin-bottom: 0px !important\"><input ng-model=nomeArquivos name=arquivos type=text disabled></md-input-container><md-container flex-gt-sm flex-offset=5><md-button class=md-raised ngf-select=uploadArquivo($files) id=file name=file ngf-multiple=true accept=* ng-disabled=bloquearFormulario>{{\'label-selecionar-arquivo\' | translate}}</md-button></md-container></div><div layout=row><md-content flex=100 flex-offset=5><md-list ng-cloak><md-list-item ng-repeat=\"arquivo in arquivos\"><div class=row><a href={{realizarDownloadArquivo(arquivo.id)}}>{{arquivo.nome== undefined? arquivo.name : arquivo.nome}}</a></div><div class=row><md-icon ng-click=removerArquivo($event,$index); aria-label=Excluir>close</md-icon></div></md-list-item></md-list></md-content></div>");
$templateCache.put("scripts-servicos/app/directives/utilitarios/templates/documentosConcluirAtendimento.html","<div layout=column layout-margin=5><div><md-button type=file id=file name=file multiple ngf-select=addDocumentoAnaliseRequerimento($files) accept=image/* class=md-raised>{{\'btn-inserir-documento\' | translate}}</md-button></div><md-content><div class=md-dialog-content><div layout=row><md-content style=\"width: 70% !important\" flex-offset=5><md-list ng-cloak><md-list-item ng-repeat=\"documento in docConcluirAtendimento\"><div class=row><a href=\"\">{{documento.name}}</a></div><div class=row><md-icon ng-click=\"removerDocumentoAnaliseRequerimento(documento.id, $index);\" aria-label=Fechar>close</md-icon></div></md-list-item></md-list></md-content></div></div></md-content></div>");
$templateCache.put("scripts-servicos/app/directives/utilitarios/templates/fotosImovel.html","<div layout=row layout-xs=column flex=100 layout-align=\"start center\" ng-show=!visualizar><label>{{\'fotos-imovel\' | translate}}:</label><md-button flex=15 flex-xs=100 class=md-primary ng-click=abrirModalUploadFotos($event);>{{\'adicionar-foto\' | translate}}</md-button></div><div layout=row layout-xs=column flex=100 layout-align=\"start center\" ng-show=visualizar style=\"margin-bottom: 2%\"><label class=detalhar-label>{{\'label-fotos-imovel\' | translate}}:</label></div><div layout=row layout-xs=column hide-xs><md-container flex=25 flex-offset=5 flex-xs=100 flex-offset-xs=0><img alt=\"\" class=hand visualizar-imagem src={{previewFoto[0]}} style=\"width: 100px; height: 100px\" ng-show=\"previewFoto.length > 0\"></md-container><md-container flex=25 flex-offset=5 flex-xs=100 flex-offset-xs=0><img alt=\"\" class=hand visualizar-imagem src={{previewFoto[1]}} style=\"width: 100px; height: 100px\" ng-show=\"previewFoto.length > 1\"></md-container><md-container flex=25 flex-offset=5 flex-xs=100 flex-offset-xs=0 ng-show=\"previewFoto.length > 2\"><img alt=\"\" class=hand visualizar-imagem src={{previewFoto[2]}} style=\"width: 100px; height: 100px\"></md-container><md-container flex=25 flex-offset=5 flex-xs=100 flex-offset-xs=0 ng-show=\"previewFoto.length > 3\"><img alt=\"\" class=hand visualizar-imagem src={{previewFoto[3]}} style=\"width: 100px; height: 100px\"></md-container></div><!--Fotos para Mobile--><div layout-xs=row show-xs hide-gt-xs><md-container flex=25 flex-offset=5 flex-xs=100 flex-offset-xs=0><img alt=\"\" src={{previewFoto[0]}} style=\"width: 100px; height: 100px\"></md-container><md-container flex=25 flex-offset=5 flex-xs=100 flex-offset-xs=0><img alt=\"\" src={{previewFoto[1]}} style=\"width: 100px; height: 100px\"></md-container></div><div layout-xs=row show-xs hide-gt-xs><md-container flex=25 flex-offset=5 flex-xs=100 flex-offset-xs=0><img alt=\"\" src={{previewFoto[2]}} style=\"width: 100px; height: 100px\"></md-container><md-container flex=25 flex-offset=5 flex-xs=100 flex-offset-xs=0><img alt=\"\" src={{previewFoto[3]}} style=\"width: 100px; height: 100px\"></md-container></div>");
$templateCache.put("scripts-servicos/app/directives/utilitarios/templates/visualizarImagem.html","<md-dialog aria-label=VisualizarFotos flex=60 ng-cloak><md-toolbar><div class=md-toolbar-tools><md-container layout=row><h2>{{\'label-visualizar-imagem\' | translate}}</h2></md-container><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon aria-label=\"Fechar caixa de diálogo\">close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><md-container flex=100 flex-xs=100 flex-offset-xs=0><img alt=\"\" src={{imagem}} style=\"margin: auto; max-width: 100%\"></md-container></div></md-dialog-content></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/arquivosAjuda.html","<section id=conteudo><div layout=row ng-repeat=\"arquivo in arquivosAjuda\"><ul><li><a ng-href={{downloadArquivo(arquivo.id)}}>{{arquivo.nome}}</a></li></ul></div></section>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/dadosComplementar.html","<div layout=column layout-margin=5><div ng-repeat=\"dado in camposDadosComplementares\" layout-margin=5><div layout=row layout-xs=column ng-if=\"isCampoTexto(dado) && !isCampoTextArea(dado)\"><label class=detalhar-label>{{dado.nomeCampo}}:</label><label ng-bind=dado.valor></label></div><div layout=row layout-xs=column class=marginTopNone><label class=detalhar-label>{{dado.campoDadoComplementar.nomeCampo}}:</label><label ng-if=dado.valorTexto>{{dado.valorTexto}}</label><label ng-if=dado.valorDecimal>{{dado.valorDecimal}}</label><label ng-if=dado.valorInteiro>{{dado.valorInteiro}}</label><label ng-if=dado.valorSelecaoExclusiva>{{dado.valorSelecaoExclusiva}}</label><label ng-if=dado.valorCpfCnpj>{{dado.valorCpfCnpj | cpfcnpj}}</label><label ng-if=dado.valorData>{{dado.valorData | dataformatada}}</label><label ng-if=dado.valorComboDadoComplementar>{{dado.valorComboDadoComplementar.descricao}}</label><label ng-if=dado.cartorio>{{dado.cartorio.nome}}</label><label ng-if=dado.arquivo>{{dado.arquivo.nome}}</label><label ng-if=dado.valorUnidadeAtendimento>{{dado.valorUnidadeAtendimento}}</label></div><div layout=row ng-if=dado.endereco flex=100><endereco-dado-complementar flex=100 dado=dado></endereco-dado-complementar></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharAdquirenteTitular.html","<div layout=column layout-margin=5 layout-xs=column><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'cpf-cnpj\' | translate}}:</label><label ng-bind=\"requerimento.adquirenteTitular.cpfCnpj | cpfcnpj\"></label></div><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'nome-razao-social\' | translate}}:</label><label ng-bind=requerimento.adquirenteTitular.nome></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=15 flex-xs=100><label class=detalhar-label>{{\'cep\' | translate}}</label><label ng-bind=\"requerimento.adquirenteTitular.endereco.cep | cep\"></label></div><div layout=row flex=5 flex-xs=100><label class=detalhar-label>{{\'uf\' | translate}}</label><label ng-bind=requerimento.adquirenteTitular.endereco.uf></label></div><div layout=row flex=20 flex-xs=100><label class=detalhar-label>{{\'municipio\' | translate}}</label><label ng-bind=requerimento.adquirenteTitular.endereco.municipio></label></div><div layout=row flex=20 flex-xs=100><label class=detalhar-label>{{\'tipo-logradouro\' | translate}}</label><label ng-bind=requerimento.adquirenteTitular.endereco.tipoLogradouro></label></div></div><div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5><div layout=row flex=20 flex-xs=100><label class=detalhar-label>{{\'numero\' | translate}}</label><label ng-bind=requerimento.adquirenteTitular.endereco.numero></label></div><div layout=row flex=45 flex-xs=100><label class=detalhar-label>{{\'logradouro\' | translate}}</label><label ng-bind=requerimento.adquirenteTitular.endereco.logradouro></label></div><div layout=row flex=45 flex-xs=100><label class=detalhar-label>{{\'bairro\' | translate}}</label><label ng-bind=requerimento.adquirenteTitular.endereco.bairro></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=40 flex-xs=100><label class=detalhar-label>{{\'complemento\' | translate}}</label><label ng-bind=\"requerimento.adquirenteTitular.endereco.complemento ? requerimento.requerente.endereco.complemento : \'Não informado\'\"></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'email\' | translate}}</label><label ng-bind=requerimento.adquirenteTitular.email></label></div></div><div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-fixo\' | translate}}</label><label ng-bind=\"requerimento.adquirenteTitular.telefoneFixo | telefonebr\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-celular\' | translate}}</label><label ng-bind=\"requerimento.adquirenteTitular.telefoneCelular | telefonebr\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-comercial\' | translate}}</label><label ng-bind=\"requerimento.adquirenteTitular.telefoneComercial | telefonebr\"></label></div></div><div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5><label class=detalhar-label>{{\'o-imovel-foi-transferido\' | translate}}?</label><label>{{requerimento.transferenciaImovel.transferidoParaMaisDeUmaPessoa ? \'rb-sim\' : \'rb-nao\' | translate}}</label></div><div layout=column layout-margin=5 layout-xs=column ng-if=\"requerimento.imovel.transferenciaImovel.pessoas.length > 0\"><md-container class=table-responsive-vertical><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'cpf-cnpj\' | translate | uppercase}}</th><th md-column class=texto-centro>{{\'nome-razao-social\' | translate | uppercase}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"pessoa in requerimento.imovel.transferenciaImovel.pessoas\"><td md-cell class=texto-centro style=\"color: rgba(0,0,0,0.87) !important\">{{pessoa.cpfCnpj}}</td><td md-cell class=texto-centro style=\"color: rgba(0,0,0,0.87) !important\">{{pessoa.nome}}</td></tr></tbody></table></md-table-container></md-container></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharAreaEvento.html","<div layout=column layout-margin=5 layout-xs=column><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'cep\' | translate}}</label><label ng-bind=\"requerimento.requerente.evento.endereco.cep | cep\" name=\"Área do Evento - CEP\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'uf\' | translate}}</label><label ng-bind=requerimento.requerente.evento.endereco.uf name=\"Área do Evento - UF\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'municipio\' | translate}}</label><label ng-bind=requerimento.requerente.evento.endereco.municipio name=\"Área do Evento - Município\"></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=45 flex-xs=100><label class=detalhar-label>{{\'logradouro\' | translate}}</label><label ng-bind=requerimento.requerente.evento.endereco.logradouro name=\"Área do Evento - Logradouro\"></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'numero\' | translate}}</label><label ng-bind=requerimento.requerente.evento.endereco.numero name=\"Área do Evento - Número\"></label></div><div layout=row flex=30 flex-xs=100><label class=detalhar-label>{{\'bairro\' | translate}}</label><label ng-bind=requerimento.requerente.evento.endereco.bairro name=\"Área do Evento - Bairro\"></label></div></div><div layout=row layout-xs=column flex=50 flex-xs=100 layout-margin=5><div><label class=detalhar-label>{{\'complemento\' | translate}}</label><label ng-bind=\"requerimento.requerente.evento.endereco.complemento ? requerimento.requerente.evento.endereco.complemento : \'Não Informado\'\" name=\"Área do Evento - Complemento\"></label></div></div><div layout=row layout-xs=column flex=50 flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'area-exploracao-info\' | translate}}:</label><label>{{requerimento.requerente.evento.areaExplocaoIgualAreaAbrangencia ? \'Sim\' : \'Não\'}}</label></div></div><div layout=row layout-xs=column flex=100 layout-margin=5 ng-show=requerimento.requerente.evento.areaAbrangencia><div layout=row flex=30 flex-xs=100><label class=detalhar-label>{{\'area-abrangencia-campo\' | translate}}:</label><label ng-bind=\"(requerimento.requerente.evento.areaAbrangencia | currency).replace(\'R$\',\'\')\" name=\"Área do Evento - Área Abrangência\"></label></div><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'area-exploracao-campo\' | translate}}</label><label ng-bind=\"(requerimento.requerente.evento.areaExploracao | currency).replace(\'R$\',\'\')\" name=\"Área do Evento - Área Exploracao\"></label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharAutorizacao.html","<div layout=column layout-margin=5 layout-xs=column><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'finalidade\' | translate}}:</label><label ng-bind=requerimento.autorizacaoObra.finalidade></label></div></div><div layout=row layout-xs=columun flex-xs=100 layout-margin=5><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'detalhar\' | translate}}:</label><label ng-bind=requerimento.autorizacaoObra.descricao></label></div></div><div layout=row><div layout=row flex=50 flex-xs=100 layout-margin=5 style=\"margin-bottom: 0px\"><label class=detalhar-label>{{\'intervencao-obra\' | translate}}</label></div><div layout=row flex=50 flex-xs=100 layout-margin=5 style=\"margin-bottom: 0px\"><label class=detalhar-label>{{\'prazo-utilizacao\' | translate}}</label></div></div><div layout=row layout-xs=columun flex-xs=100 style=\"margin-top: 0px\"><div layout=row flex=50 layout-margin=5><div layout=row flex=50 flex-xs=100 style=\"margin-top: 0px\"><label class=detalhar-label>{{\'previsao-inicio\' | translate}}:</label><label ng-bind=\"requerimento.autorizacaoObra.previsaoInicio | dataformatada\"></label></div><div layout=row flex=50 flex-xs=100 style=\"margin-top: 0px\"><label class=detalhar-label>{{\'duracao-mes\' | translate}}:</label><label ng-bind=requerimento.autorizacaoObra.duracaoMeses></label></div></div><div layout=row flex=50 layout-margin=5><div layout=row flex=50 flex-xs=100 style=\"margin-top: 0px\"><label class=detalhar-label>{{\'prazo-meses\' | translate}}:</label><label ng-bind=requerimento.autorizacaoObra.prazoMeses></label></div></div></div><div layout=row layout-xs=columun flex-xs=100 layout-margin=5><label class=detalhar-label>{{\'possuo-recurso\' | translate}}</label></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharCancelamentoCobranca.html","<div layout=row layout-margin=5 layout-xs=column><div layout=column layout-xs=column flex=30 flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-motivo-pedido\' | translate}}:</label><label ng-bind=requerimento.cancelamentoCobranca.motivoCancelamentoCobranca.motivo></label></div><div layout=row><label class=detalhar-label>{{\'label-descricao\' | translate}}:</label><label ng-bind=requerimento.cancelamentoCobranca.descricao></label></div></div><div layout=column layout-xs=column flex=70 flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label style=\"padding-left: 30px\">{{\'label-num-processo-atendimento-requerimento\' | translate}}:</label><label ng-bind=requerimento.cancelamentoCobranca.numProcesso></label></div><div layout=row><label class=detalhar-label>{{\'label-anos-exercicios\' | translate}}:</label><label class=quebra-linha ng-if=!requerimento.cancelamentoCobranca.cancelarDebitos ng-bind=labelAnosDebitos></label><label class=quebra-linha ng-if=requerimento.cancelamentoCobranca.cancelarDebitos>{{\'label-cancelar-todos-debitos\' | translate}}</label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharCancelamentoUtilizacao.html","<style>.borda-utilizacao{\n    border: solid black 1px;\n    display: block;\n    width: 130%;\n  }\n\n  .borda-sem-top-utilizacao{\n    border: solid black 1px;\n    border-top: none;\n    display: block;\n    width: 130%;\n  }\n\n  .borda-left-utilizacao{\n    border-left: solid black 1px;\n  }</style><div layout=column><div layout=row layout-xs=column><md-container><label class=detalhar-label>{{\'selecionar-utilizacao-cancelamento\' | translate}}</label></md-container></div><div layout=row layout-xs=column><md-list ng-cloak><md-list-item ng-repeat=\"utilizacaoImovel in requerimento.cancelamentoUtilizacao.utilizaoesImovel\" class=\"{{$index == 0 ? \'borda-utilizacao\' : \'borda-sem-top-utilizacao\'}}\"><div class=\"column borda-left-utilizacao\" layout-padding=5 flex=100><div layout=row flex=80 layout-xs=column><label>{{\'label-instrumento-destinacao\' | translate}}:</label><label flex-offset=5>{{utilizacaoImovel.instrumentoDestinacao}}</label></div><div layout=row flex=80 layout-xs=column><label>{{\'label-vigencia\' | translate}}:</label><label flex-offset=5>{{utilizacaoImovel.dataVigencia | date:\'MM/dd/yyyy\'}}</label></div></div></md-list-item></md-list></div><div layout=row layout-xs=column layout-margin=5><div flex=60><label class=detalhar-label>{{\'motivo\' | translate}}:</label><label ng-bind=labelTiposMotivoCancelamento></label></div></div><div layout=row layout-xs=column layout-margin=5><div flex><label class=detalhar-label>{{\'justificativa-cancelamento\' | translate}}:</label><label ng-bind=requerimento.cancelamentoUtilizacao.justificativa></label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharDadosRegimeContrato.html","<h2 class=texto-verde>{{\'label-dados-regime-contrato\' | translate | uppercase}}</h2><div layout=row layout-xs=column layout-margin=5><div layout=row flex=30 layout-xs=column><label class=detalhar-label>{{\'label-regime-contrato\' | translate}}:</label><label ng-bind=requerimento.regimeContrato.tipoRegime.descricao></label></div><div layout=row flex=70 layout-xs=column><label class=detalhar-label>{{\'label-uso-pretendido\' | translate}}:</label><label ng-bind=requerimento.regimeContrato.usoPretendido.descricao></label></div></div><div layout=row layout-xs=column layout-margin=5><label class=detalhar-label>{{\'label-descricao-uso\' | translate}}:</label><label ng-bind=requerimento.regimeContrato.descricaoDoUso></label></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharDocumento.html","<div layout=column layout-margin=5><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=100 flex-xs=100><label class=detalhar-label>{{\'tipo-solicitante\' | translate}}</label><label ng-bind=tipoSolicitante></label></div></div><md-container class=\"infoSocioEconomica table-responsive-vertical\" ng-show=\"documento.documentosObrigatorios.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-documentos-obrigatorios\' | translate}}</th><th md-column class=texto-centro>{{\'label-acao\' | translate }}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"doc in documento.documentosObrigatorios | orderBy: \'ordem\'\"><td md-cell>{{doc.nome}}</td><td md-cell><ng-md-icon modal-documento=doc modal-id-requerimento=idRequerimento layout-align=\"start center\" style=fill:#788489 class=hand icon=description></ng-md-icon></td></tr></tbody></table></md-table-container></md-container><md-container class=\"infoSocioEconomica table-responsive-vertical\" ng-show=\"documento.documentosComplementares.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-documentos-complementares\' | translate}}</th><th md-column class=texto-centro>{{\'label-situacao\' | translate }}</th><th md-column class=texto-centro>{{\'label-acao\' | translate }}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"doc in documento.documentosComplementares | orderBy: \'ordem\'\"><td md-cell>{{doc.nome}}</td><td md-cell>{{formatarTextoSituacao(doc.situacaoDocumentoComplementar)}}</td><td md-cell><div ng-if=\"doc.files.length > 0\"><ng-md-icon modal-documento=doc modal-id-requerimento=idRequerimento layout-align=\"start center\" style=fill:#788489 class=hand icon=description></ng-md-icon></div></td></tr></tbody></table></md-table-container></md-container><md-container class=\"infoSocioEconomica table-responsive-vertical\" ng-show=\"documento.outrosDocumentos.length > 0\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-outro-documentos\' | translate}}</th><th md-column class=texto-centro>{{\'label-acao\' | translate }}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"doc in documento.outrosDocumentos | orderBy: \'ordem\'\"><td md-cell>{{doc.nome}}</td><td md-cell><div ng-if=\"doc.files.length > 0\"><ng-md-icon modal-documento=doc modal-id-requerimento=idRequerimento layout-align=\"start center\" style=fill:#788489 class=hand icon=description></ng-md-icon></div></td></tr></tbody></table></md-table-container></md-container></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharEnderecoCorrespondencia.html","<div layout-margin=5 layout-xs=column><div layout=row layout-xs=column flex=100 flex-xs=100 layout-margin=5><div layout=row flex=33 flex-xs=100><div layout=row flex=50><label class=detalhar-label>{{\'cep\' | translate}}</label><label ng-bind=\"requerimento.requerente.enderecoCorrespondencia.cep | cep\"></label></div><div layout=row flex=50><label class=detalhar-label>{{\'uf\' | translate}}</label><label ng-bind=requerimento.requerente.enderecoCorrespondencia.uf></label></div></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'municipio\' | translate}}</label><label ng-bind=requerimento.requerente.enderecoCorrespondencia.municipio></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'tipo-logradouro\' | translate}}</label><label ng-bind=requerimento.requerente.enderecoCorrespondencia.tipoLogradouro></label></div></div><div layout=row layout-xs=column flex=100 flex-xs=100 layout-margin=5><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'logradouro\' | translate}}</label><label ng-bind=requerimento.requerente.enderecoCorrespondencia.logradouro></label></div><div layout=row flex=33 flex-xs=100><div layout=row flex=50><label class=detalhar-label>{{\'numero\' | translate}}</label><label ng-bind=requerimento.requerente.enderecoCorrespondencia.numero></label></div><div layout=row flex=50><label class=detalhar-label>{{\'bairro\' | translate}}</label><label ng-bind=requerimento.requerente.enderecoCorrespondencia.bairro></label></div></div><div layout=row flex=33><label class=detalhar-label>{{\'complemento\' | translate}}</label><label ng-bind=\"requerimento.requerente.enderecoCorrespondencia.complemento ? requerimento.requerente.enderecoCorrespondencia.complemento: \'-\'\"></label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharEvento.html","<div layout=column layout-margin=5 layout-xs=column><div layout=row flex=100 flex-xs=100 layout-margin=5><label class=detalhar-label>{{\'nome-evento\' | translate}}:</label><label ng-bind=requerimento.requerente.evento.nome></label></div><div layout=row flex=100 flex-xs=100 layout-margin=5><label class=detalhar-label>{{\'descricao-evento\' | translate}}:</label><label ng-bind=requerimento.requerente.evento.descricao></label></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><label class=detalhar-label>{{\'natureza\' | translate}}:</label><label ng-bind=requerimento.requerente.evento.naturezas[0].descricao></label></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><label class=detalhar-label>{{\'informacao-economica-comercial\' | translate}}:</label><label ng-bind=requerimento.requerente.evento.informacoesEconomicasComerciais[0].descricao></label></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'periodo-utilizacao-area\' | translate}}:</label><label>{{dataInicialUtilizacao | dataformatada}} a {{dataFinalUtilizacao | dataformatada}} (</label><label ng-if=\"diasUtilizacao == 0 || diasUtilizacao > 1\">{{diasUtilizacao}} dias)</label><label ng-if=\"diasUtilizacao == 1\">{{diasUtilizacao}} dia)</label></div><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'data-de-realizacao-evento\' | translate}}:</label><label>{{dataInicialRealizacao | dataformatada}} a {{dataFinalRealizacao | dataformatada}}</label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharFormaAquisicao.html","<div layout=column layout-margin=5 layout-xs=column><div layout=column layout-xs=column flex=33 flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'valorEstimado\' | translate}}</label><label ng-bind=requerimento.imovel.valorImovel></label></div><div layout=row><label class=detalhar-label>{{\'formaPagamento\' | translate}}</label><label ng-bind=requerimento.imovel.formaPagamento></label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharImovel.html","<div layout-margin=5><div layout=column layout-margin=5 layout-xs=column ng-if=\"tipo==\'CONSULTA_DOMINIALIDADE\' || tipo==\'NOVA_CONSULTA_DOMINIALIDADE\'\"><div layout=row layout-xs=column flex-xs=100><div flex=50 flex-xs=100 layout=row><label class=detalhar-label>{{\'label-n-matricula-transcricao\' | translate}}:</label><label ng-bind=requerimento.imovel.nuMatriculaTransicao></label></div></div></div><div layout=column layout-margin=5 layout-xs=column ng-if=\"tipo==\'CONSULTA_DOMINIALIDADE\' || tipo==\'NOVA_CONSULTA_DOMINIALIDADE\'\"><div layout=row layout-xs=column flex-xs=100><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'label-n-processo\' | translate}}:</label><label ng-bind=requerimento.imovel.nuProcesso></label></div><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'label-classificacao-imovel\' | translate}}:</label><label ng-bind=requerimento.imovel.classificacaoImovel></label></div><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'label-n-inscricao-municipal-rural-incra\' | translate}}:</label><label ng-bind=requerimento.imovel.nuInscricao></label></div></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div flex=33 flex-xs=100 layout=row ng-if=requerimento.imovel.rip><label class=detalhar-label>{{\'rip\' | translate}}:</label><label ng-bind=\"requerimento.imovel.rip | rip\"></label></div><div flex=33 flex-xs=100 layout=row><label class=detalhar-label>{{\'cep\' | translate}}</label><label ng-bind=\"requerimento.imovel.endereco.cep | cep\"></label></div><div flex=33 flex-xs=100 layout=row><label class=detalhar-label>{{\'uf\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.uf></label></div><div flex=33 flex-xs=100 layout=row><label class=detalhar-label>{{\'municipio\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.municipio></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div flex=33 flex-xs=100 layout=row><label class=detalhar-label>{{\'tipo-logradouro\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.tipoLogradouro></label></div><div flex=33 flex-xs=100 layout=row><label class=detalhar-label>{{\'logradouro\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.logradouro></label></div><div flex=33></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div flex=33 flex-xs=100 layout=row><label class=detalhar-label>{{\'numero\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.numero></label></div><div flex=33 flex-xs=100 layout=row><label class=detalhar-label>{{\'bairro\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.bairro></label></div><div flex=33 flex-xs=100 layout=row><label class=detalhar-label>{{\'complemento\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.complemento></label></div></div><div layout=row layout-margin=5><div flex=35 layout=row layout-xs=column flex-xs=100 ng-if=\"verificarTipo() || isValorArea() || isAutorizacaoObra() || isIsencao()\"><label class=detalhar-label>{{\'area-terreno\' | translate}}:</label><label ng-bind=\"requerimento.imovel.areaTerreno | number : 2\"></label></div><div layout=row flex=25 layout-xs=column flex-xs=100 ng-show=\"verificarTipo() || isValorArea() || isConsultaDominialidade() || isNovaConsultaDominialidade()\"><label class=detalhar-label>{{\'tipo-imovel\' | translate}}:</label><label ng-bind=requerimento.imovel.tipoImovel></label></div><div layout=row flex=25 layout-xs=column flex-xs=100 ng-show=\"isConsultaDominialidade() || isNovaConsultaDominialidade()\"><label class=detalhar-label>{{\'possui-area-construida\' | translate}}:</label><label>{{requerimento.imovel.statusAreaConstruida ? \'sim\' : \'nao\' | translate}}</label></div><div layout=row flex=25 layout-xs=column flex-xs=100 ng-show=\"verificarTipo() || isValorArea() || isConsultaDominialidade() || isNovaConsultaDominialidade()\"><label class=detalhar-label>{{\'area-construida\' | translate}}:</label><label ng-bind=\"(requerimento.imovel.areaConstruida | currency).replace(\'R$\',\'\')\"></label></div></div><div layout=row layout-xs=column flex=100 flex-xs=100 layout-margin=5 ng-show=verificarTipo();><fotos-imovel flex=100 imovel=requerimento.imovel visualizar=true></fotos-imovel></div><div ng-include=\"\'scripts-servicos/app/directives/detalhar-requerimento/templates/imovel/detalharImovelDadosUtilizacao.html\'\" layout=column flex tabindex=-1 role=main ng-if=\"!isTransferenciaTitular() && !isAutorizacaoObra\"></div><div ng-include=\"\'scripts-servicos/app/directives/detalhar-requerimento/templates/imovel/detalharImovelTransferenciaTitular.html\'\" layout=column flex tabindex=-1 role=main ng-if=\"isTransferenciaTitular() && !isAutorizacaoObra()\"></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharImovelUniao.html","<div layout=column layout-margin=5><div layout=row layout-xs=column ng-show=\"!requerimento.isRemicaoAforamento || requerimento.isRemicaoAforamento == undefined\"><label class=detalhar-label>{{\'endereco-requerenet-igual-imovel\' | translate}}:</label><label ng-bind=\"requerimento.imovel.statusRequerenteImovel ? \'Sim\' : \'Não\'\"></label></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=15 flex-xs=100><label class=detalhar-label>{{\'cep\' | translate}}</label><label ng-bind=\"requerimento.imovel.endereco.cep | cep\"></label></div><div layout=row flex=15 flex-xs=100><label class=detalhar-label>{{\'uf\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.uf></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'municipio\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.municipio></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'logradouro\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.logradouro></label></div><div layout=row flex=20 flex-xs=100><label class=detalhar-label>{{\'numero\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.numero></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=40 flex-xs=100><label class=detalhar-label>{{\'tipo-logradouro\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.tipoLogradouro></label></div><div layout=row flex=40 flex-xs=100><label class=detalhar-label>{{\'bairro\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.bairro></label></div><div layout=row flex=65 flex-xs=100><label class=detalhar-label>{{\'complemento\' | translate}}</label><label ng-bind=requerimento.imovel.endereco.complemento></label></div></div><!--    <div layout=\"row\" layout-xs=\"column\" flex-xs=\"100\" layout-margin=\"5\">\n       <label class=\"detalhar-label\">{{\'indique-localizacao-mapa\' | translate}}</label>\n    </div>\n\n    <div layout=\"row\" layout-xs=\"column\" flex-xs=\"100\" layout-margin=\"5\">\n        <md-input-container flex=\"100\">\n            <md-content>\n                <mapa></mapa>\n            </md-content>\n        </md-input-container>\n    </div>--><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'tipo-imovel\' | translate}}:</label><label ng-bind=requerimento.imovel.tipoImovel></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'area-terreno\' | translate}}:</label><label ng-bind=\"(requerimento.imovel.areaTerreno | currency).replace(\'R$\',\'\')\"></label></div><div layout=row flex=25 flex-xs=100 ng-show=\"!requerimento.isRemicaoAforamento || requerimento.isRemicaoAforamento == undefined\"><label class=detalhar-label>{{\'possui-area-construida\' | translate}}:</label><label ng-bind=\"requerimento.imovel.statusAreaConstruida ? \'Sim\' : \'Não\'\"></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'area-construida\' | translate}}:</label><label ng-bind=\"(requerimento.imovel.areaConstruida | currency).replace(\'R$\',\'\')\"></label></div></div><div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5 ng-show=\"!requerimento.isRemicaoAforamento || requerimento.isRemicaoAforamento == undefined\"><fotos-imovel flex=100 imovel=requerimento.imovel visualizar=true></fotos-imovel></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5 ng-show=\"!requerimento.isRemicaoAforamento || requerimento.isRemicaoAforamento == undefined\"><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'uso-principal\' | translate}}:</label><label ng-bind=requerimento.imovel.usoPrincipal></label></div><div layout=row flex=25 flex-xs=100 ng-show=requerimento.imovel.especificacaoUso><label class=detalhar-label>{{\'especificacao-uso\' | translate}}:</label><label ng-bind=requerimento.imovel.especificacaoUso></label></div><div layout=row flex=100 ng-show=\"!requerimento.isRemicaoAforamento || requerimento.isRemicaoAforamento == undefined\"><label class=detalhar-label>{{\'descricao-uso\' | translate}}:</label><label ng-bind=requerimento.imovel.descricaoUso></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5 ng-show=\"!requerimento.isRemicaoAforamento || requerimento.isRemicaoAforamento == undefined\"><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'data-ocupacao\' | translate}}:</label><label ng-bind=\"requerimento.imovel.dataInicioOcupacao | dataformatada\"></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'numero-familias\' | translate}}:</label><label ng-bind=requerimento.imovel.numeroFamilias></label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharInformacoesSocioEconomicas.html","<div layout=column layout-margin=5><div layout=row layout-xs=column><div layout=column layout-xs=column flex=50 flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'label-profissao\' | translate}}</label><label ng-bind=requerimento.requerente.profissao.tipoProfissao.descricao></label></div><div layout=row><label class=detalhar-label>{{\'label-renda-familiar-mensal\' | translate}}</label><label ng-bind=\"requerimento.requerente.rendaFamiliar | number: 2\"></label></div></div><div layout=column layout-xs=column flex=50 flex-xs=100 layout-margin=5><div layout=row><label class=detalhar-label>{{\'possui-dependentes\' | translate}}</label><label ng-bind=\"requerimento.requerente.possuiDependentes ? \'Sim\' : \'Não\'\"></label></div></div></div><div ng-show=\"requerimento.requerente.dependentes.length > 0\"><div class=\"infoSocioEconomica table-responsive-vertical\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-cpf\' | translate | uppercase}}</th><th md-column class=texto-centro>{{\'label-renda-mensal\' | translate | uppercase}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"dependente in requerimento.requerente.dependentes\"><td md-cell class=texto-centro-corpo>{{dependente.cpf | cpfcnpj}}</td><td md-cell class=texto-centro-corpo>{{dependente.renda | currency:\'R$\'}}</td></tr></tbody></table></md-table-container></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharPrioridadeAtendimento.html","<span class=\"texto-verde detalhar-label ng-binding\">{{\'label-prioridade-atendimento\' | translate | uppercase}}</span><div layout=row flex=100><div layout=column layout-margin=5 class=\"layout-margin layout-column\"><h3 class=form-prioridade>{{\'prioridade-atendimento\' | translate}}*:</h3><div layout=row layout-xs=column flex-xs=100 layout-margin=5 class=\"layout-margin layout-xs-column detalhar-prioridade\"><md-input-container><md-checkbox ng-disabled=bloquear ng-model=requerimento.prioridadeAtendimento.prioridadeBetween class=md-primary>{{\'label-idosos-idade-entre\' | translate}}</md-checkbox><a ng-href=http://www.planalto.gov.br/ccivil_03/leis/2003/L10.741.htm{{hash}} target=_blank>(Lei nº 10.741/2003)</a></md-input-container></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5 class=\"layout-margin layout-xs-column detalhar-prioridade\"><md-input-container><md-checkbox ng-disabled=bloquear ng-model=requerimento.prioridadeAtendimento.prioridadeAcima class=md-primary>{{\'label-idosos-idade-acima\' | translate}}</md-checkbox><a ng-href=http://www.planalto.gov.br/ccivil_03/leis/2003/L10.741.htm{{hash}} target=_blank>(Lei nº 10.741/2003)</a></md-input-container></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5 class=\"layout-margin layout-xs-column detalhar-prioridade\"><md-input-container><md-checkbox ng-disabled=bloquear ng-model=requerimento.prioridadeAtendimento.prioridadeDoenca class=md-primary>{{\'label-doencas-enquadradas\' | translate}}</md-checkbox><a ng-href=http://www.planalto.gov.br/ccivil_03/leis/L7713compilada.htm{{hash}} target=_blank>(Lei 10.048/2000)</a></md-input-container></div><div ng-disabled=bloquear><div layout=row ng-show=\"requerimento.prioridadeAtendimento.prioridadeDoenca === true\"><label>{{\'label-upload-relatorio-medico\' | translate}}</label></div><div layout=row flex=33 flex-xs=100 ng-show=\"requerimento.prioridadeAtendimento.prioridadeDoenca === true\"><md-input-container><input ng-if=!bloquear ng-model=requerimento.prioridadeAtendimento.arquivos[0].name name=\"{{\'label-nome-arquivo\' | translate}}\" disabled></md-input-container><md-button ng-if=!bloquear ng-disabled=bloquear ngf-multiple=true class=md-primary style=\"margin-top:1px; margin-left:1px; height:0px\">{{\'selecionar-arquivo\' | translate}}</md-button></div><div class=detalhar-prioridade ng-if=\"requerimento.prioridadeAtendimento.arquivos.length > 0\" ng-repeat=\"arquivo in requerimento.prioridadeAtendimento.arquivos\" layout=row><a href=javascript:; ng-href={{download(arquivo.id)}}>{{arquivo.name}}{{arquivo.nome}}</a></div></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharRepresentanteLegal.html","<div layout=column layout-margin=5 layout-xs=column><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'cpf-cnpj\' | translate}}</label><label ng-bind=\"requerimento.requerente.representanteLegal.cpfCnpj | cpfcnpj\"></label></div><div layout=row flex=\"verificarTipo() ? 50 : 75\" flex-xs=100><label class=detalhar-label>{{\'nome\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.nome></label></div><div layout=row flex-xs=100 flex=25 ng-show=verificarTipo()><label class=detalhar-label>{{\'cargo\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.cargo></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'cargo\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.cargo></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'cep\' | translate}}</label><label ng-bind=\"requerimento.requerente.representanteLegal.endereco.cep | cep\"></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'uf\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.endereco.uf></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'municipio\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.endereco.municipio></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=30><label class=detalhar-label>{{\'tipo-logradouro\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.endereco.tipoLogradouro></label></div><div layout=row layout-xs=column flex=70 flex-xs=100><label class=detalhar-label>{{\'logradouro\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.endereco.logradouro></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=20 flex-xs=100><label class=detalhar-label>{{\'numero\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.endereco.numero></label></div><div layout=row flex=40 flex-xs=100><label class=detalhar-label>{{\'bairro\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.endereco.bairro></label></div><div layout=row flex=40 flex-xs=100><label class=detalhar-label>{{\'complemento\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.endereco.complemento></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=25><label class=detalhar-label>{{\'email\' | translate}}</label><label ng-bind=requerimento.requerente.representanteLegal.email></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-fixo\' | translate}}</label><label ng-bind=\"requerimento.requerente.representanteLegal.telefoneFixo | telefonebr\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-celular\' | translate}}</label><label ng-bind=\"requerimento.requerente.representanteLegal.telefoneCelular | telefonebr\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-comercial\' | translate}}</label><label ng-bind=\"requerimento.requerente.representanteLegal.telefoneComercial | telefonebr\"></label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharRequerente.html","<div layout=column layout-margin=5 layout-xs=column><div layout=row layout-xs=column flex flex-xs=100 layout-margin=5 class=flexStart><div layout=row flex=33 flex-xs=100 ng-show=\"!requerimento.isRemicaoAforamento || requerimento.isRemicaoAforamento == undefined\"><label class=detalhar-label>{{\'cpf-cnpj\' | translate}}</label><label ng-bind=\"requerimento.requerente.cpfCnpj | cpfcnpj\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'nome-razao-social\' | translate}}</label><label ng-bind=requerimento.requerente.nome></label></div><div layout=row flex=33 flex-xs=100 ng-show=!verificarTipo();><label class=detalhar-label>{{\'natureza\' | translate}}:</label><label ng-bind=\"requerimento.requerente.natureza ? requerimento.requerente.natureza : \'Não informado\'\"></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5 class=flexStart><div layout=row flex=33 flex-xs=100><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'cep\' | translate}}</label><label ng-bind=\"requerimento.requerente.endereco.cep | cep\"></label></div><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'uf\' | translate}}</label><label ng-bind=requerimento.requerente.endereco.uf></label></div></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'municipio\' | translate}}</label><label ng-bind=requerimento.requerente.endereco.municipio></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'tipo-logradouro\' | translate}}</label><label ng-bind=requerimento.requerente.endereco.tipoLogradouro></label></div></div><div layout=row layout-xs=column flex=33 flex-xs=100 layout-margin=5><div layout=row flex=45 flex-xs=100><label class=detalhar-label>{{\'logradouro\' | translate}}</label><label ng-bind=requerimento.requerente.endereco.logradouro></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'numero\' | translate}}</label><label ng-bind=requerimento.requerente.endereco.numero></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'bairro\' | translate}}</label><label ng-bind=requerimento.requerente.endereco.bairro></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'complemento\' | translate}}</label><label ng-bind=\"requerimento.requerente.endereco.complemento ? requerimento.requerente.endereco.complemento : \'Não informado\'\"></label></div><div layout=row flex=50 flex-xs=100 ng-show=\"!requerimento.isRemicaoAforamento || requerimento.isRemicaoAforamento == undefined\"><label class=detalhar-label>{{\'endereco-correspondencia\' | translate}}</label><label ng-bind=\"requerimento.requerente.possuiEnderecoCorrespondencia ? \'Sim\' : \'Não\'\"></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'email\' | translate}}</label><label ng-bind=requerimento.requerente.email></label></div></div><div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-fixo\' | translate}}</label><label ng-bind=\"requerimento.requerente.telefoneFixo | telefonebr\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-celular\' | translate}}</label><label ng-bind=\"requerimento.requerente.telefoneCelular | telefonebr\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-comercial\' | translate}}</label><label ng-bind=\"requerimento.requerente.telefoneComercial | telefonebr\"></label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharTitulo.html","<h1 class=texto-verde>{{requerimento.servico.titulo}} <span ng-show=requerimento.atendimento.numero>- {{requerimento.atendimento.numero}} - DATA: {{requerimento.dataEnvio | dataformatada}}</span></h1>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharTransferenciaImovel.html","<div layout=column layout-margin=5 layout-xs=column>{{requerimento.transferenciaImovel}}<div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5><label class=detalhar-label>{{\'o-imovel-foi-transferido\' | translate}}?</label><label>{{requerimento.imovel.transferenciaImovel.transferidoParaMaisDeUmaPessoa ? \'sim\' : \'nao\' | translate}}</label></div><div layout=column layout-margin=5 layout-xs=column ng-if=\"requerimento.imovel.transferenciaImovel.pessoas.length > 0\"><md-container class=\"table-responsive-vertical infoSocioEconomica\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'cpf-cnpj\' | translate | uppercase}}</th><th md-column class=texto-centro>{{\'nome-razao-social\' | translate | uppercase}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"pessoa in requerimento.imovel.transferenciaImovel.pessoas\"><td md-cell class=texto-centro style=\"color: rgba(0,0,0,0.87) !important\">{{pessoa.cpfCnpj | cpfcnpj}}</td><td md-cell class=texto-centro style=\"color: rgba(0,0,0,0.87) !important\">{{pessoa.nome}}</td></tr></tbody></table></md-table-container></md-container></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/detalharTransmitente.html","<div layout=column layout-margin=5 layout-xs=column><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'cpf-cnpj\' | translate}}</label><label ng-bind=\"transmitente.cpfCnpj | cpfcnpj\"></label></div><div layout=row flex=\"verificarTipo() ? 50 : 75\" flex-xs=100><label class=detalhar-label>{{\'nome\' | translate}}</label><label ng-bind=transmitente.nome></label></div><div layout=row flex-xs=100 flex=25 ng-show=verificarTipo()><label class=detalhar-label>{{\'natureza\' | translate}}</label><label ng-bind=transmitente.natureza></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'cep\' | translate}}</label><label ng-bind=\"transmitente.endereco.cep | cep\"></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'uf\' | translate}}</label><label ng-bind=transmitente.endereco.uf></label></div><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'municipio\' | translate}}</label><label ng-bind=transmitente.endereco.municipio></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=30 flex-xs=100><label class=detalhar-label>{{\'tipo-logradouro\' | translate}}</label><label ng-bind=transmitente.endereco.tipoLogradouro></label></div><div layout=row layout-xs=column flex=70 flex-xs=100><label class=detalhar-label>{{\'logradouro\' | translate}}</label><label ng-bind=transmitente.endereco.logradouro></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=20 flex-xs=100><label class=detalhar-label>{{\'numero\' | translate}}</label><label ng-bind=transmitente.endereco.numero></label></div><div layout=row flex=40 flex-xs=100><label class=detalhar-label>{{\'bairro\' | translate}}</label><label ng-bind=transmitente.endereco.bairro></label></div><div layout=row flex=40 flex-xs=100><label class=detalhar-label>{{\'complemento\' | translate}}</label><label ng-bind=transmitente.endereco.complemento></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=25 flex-xs=100><label class=detalhar-label>{{\'email\' | translate}}</label><label ng-bind=\"transmitente.email ? transmitente.email : \'Não informado\'\"></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-fixo\' | translate}}</label><label ng-bind=\"transmitente.telefoneFixo | telefonebr\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-celular\' | translate}}</label><label ng-bind=\"transmitente.telefoneCelular | telefonebr\"></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'telefone-comercial\' | translate}}</label><label ng-bind=\"transmitente.telefoneComercial | telefonebr\"></label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/enderecoDadoComplementar.html","<div layout=column flex=100><div layout=row layout-xs=column flex=100 flex-xs=100 class=layoutMargin5><div layout=row flex=33 flex-xs=100><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'cep\' | translate}}</label><label ng-bind=\"dado.endereco.cep | cep\"></label></div><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'uf\' | translate}}</label><label ng-bind=dado.endereco.uf></label></div></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'municipio\' | translate}}</label><label ng-bind=dado.endereco.municipio></label></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'tipo-logradouro\' | translate}}</label><label ng-bind=dado.endereco.tipoLogradouro></label></div></div><div layout=row layout-xs=column flex=100 class=layoutMargin5><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'logradouro\' | translate}}</label><label ng-bind=dado.endereco.logradouro></label></div><div layout=row flex=33 flex-xs=100><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'numero\' | translate}}</label><label ng-bind=dado.endereco.numero></label></div><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'bairro\' | translate}}</label><label ng-bind=dado.endereco.bairro></label></div></div><div layout=row flex=33 flex-xs=100><label class=detalhar-label>{{\'complemento\' | translate}}</label><label ng-bind=\"dado.endereco.complemento ? dado.endereco.complemento: \'Não informado\'\"></label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/mensagemAutenticacao.html","<div layout=row flex-xs=100 ng-if=isAutenticado><label class=\"detalhar-label msg-dados-brasil-cidadao\">{{\'msg-dados-brasil-cidadao\' | translate}}</label></div>");
$templateCache.put("scripts-servicos/pages/requerimento-consulta-dominialidade/views/detalhar/detalhar.html","<div class=\"conteudo simbolo-lateral\" ng-init=init();><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'label-consulta-dominialidade\' | translate}}</span><div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'label-objetivo-requerimento\' | translate}}:</label><label ng-bind=requerimento.objetivoRequerimento.tipoObjetivoRequerimento.descricao></label></div><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'label-descricao-objetivo\' | translate}}:</label><label ng-bind=\"requerimento.objetivoRequerimento.descricao ? requerimento.objetivoRequerimento.descricao : \'label-nao-informado\' | translate\"></label></div></div></div><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div><!--div layout=\"row\" layout-xs=\"column\" flex-xs=\"100\" layout-margin=\"5\"\n               ng-show=\"requerimento.cartorio\">\n              <div layout=\"row\" flex=\"50\" flex-xs=\"100\">\n                  <label class=\"detalhar-label\">{{\'label-nome-cartorio\' | translate}}:</label>\n                  <label ng-bind=\"requerimento.cartorio.nome\"></label>\n              </div>\n          </div--><div ng-show=!requerimento.cartorio><detalhar-requerente requerimento=requerimento tipo=\"\'CONSULTA_DOMINIALIDADE\'\"></detalhar-requerente></div><div layout=row layout-margin=5 layout-xs=column><label class=detalhar-label>{{\'label-cartorio-registro-imoveis\' | translate}}</label><label ng-bind=\"requerimento.requerente.isRequerenteCartorio | simNao\"></label></div></div><span class=\"texto-verde detalhar-label\" ng-show=verificarExisteRepresententeLegal();>{{\'dados-representante-legal\' | translate}}</span><div ng-show=verificarExisteRepresententeLegal();><detalhar-representante-legal requerimento=requerimento tipo=\"\'CONSULTA_DOMINIALIDADE\'\"></detalhar-representante-legal></div><span class=\"texto-verde detalhar-label\">{{\'dados-imovel\' | translate}}</span><div><detalhar-imovel requerimento=requerimento tipo=\"\'CONSULTA_DOMINIALIDADE\'\"></detalhar-imovel></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/requerimento-nova-consulta-dominialidade/views/detalhar/detalhar.html","<div class=\"conteudo simbolo-lateral\" ng-init=init();><detalhar-titulo requerimento=requerimento></detalhar-titulo><mensagem-autenticacao is-autenticado=requerimento.autenticado></mensagem-autenticacao><div flex><span class=\"texto-verde detalhar-label\">{{\'label-consulta-dominialidade\' | translate}}</span><br><span class=\"texto-verde detalhar-label\">{{\'dados-requerente\' | translate}}</span><div layout-margin=5><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=50 flex-xs=100><label class=detalhar-label>{{\'label-tipo-requerente\' | translate}}:</label><label ng-bind=requerimento.tiposRequerente.descricao></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5><div layout=row flex=50 flex-xs=100 ng-if=\"requerimento.tiposRequerente.id==ID_CARTORIO\"><label class=detalhar-label>{{\'label-nome-cartorio\' | translate}}:</label><label ng-bind=requerimento.requerente.cartorio.nome></label></div></div><div layout=row layout-xs=column flex-xs=100 layout-margin=5 ng-if=\"requerimento.tiposRequerente.id==ID_AGU\"><div layout=row flex=30 flex-xs=100><label class=detalhar-label>{{\'label-num-processo\' | translate}}:</label><label ng-bind=requerimento.requerente.advogado.processo></label></div><div layout=row flex=70 flex-xs=100><label class=detalhar-label>{{\'label-ug-unidade\' | translate}}:</label><label ng-bind=requerimento.requerente.advogado.descricaoUnidade></label></div></div></div><div><detalhar-requerente requerimento=requerimento tipo=\"\'CONSULTA_DOMINIALIDADE\'\"></detalhar-requerente></div><span class=\"texto-verde detalhar-label\" ng-if=\"requerimento.tiposRequerente &&\n                          requerimento.tiposRequerente.id!=ID_PESSOA_FISICA &&\n                          requerimento.tiposRequerente.id!=ID_AGU\n                          requerimento.tiposRequerente.id!=ID_CARTORIO;\">{{\'dados-representante-legal\' | translate}}</span><div ng-if=\"requerimento.tiposRequerente &&\n            requerimento.tiposRequerente.id!=ID_PESSOA_FISICA &&\n            requerimento.tiposRequerente.id!=ID_AGU\n            requerimento.tiposRequerente.id!=ID_CARTORIO;\"><detalhar-representante-legal requerimento=requerimento tipo=\"\'CONSULTA_DOMINIALIDADE\'\"></detalhar-representante-legal></div><!--FAZER O DETALHAR AGU E CARTORIO\n        <div ng-if=\"requerimento.tiposRequerente.id==ID_AGU\"\n              style=\"margin-left: 15px;\">\n            <div id=\"requerente-agu\" layout=\"row\" layout-xs=\"column\"\n                  style=\"margin-left: 2% !important\">\n                <md-input-container flex=\"25\" flex-xs=\"100\">\n                    <label>{{\'numero-processo\' | translate}}*</label>\n                    <input ng-model=\"requerimento.requerente.advogado.processo\"\n                           name=\"Requerente - N processo\"\n                           ng-required=\"obrigatorio\">\n                </md-input-container>\n                <md-input-container flex=\"70\" flex-xs=\"100\">\n                    <label>{{\'ug-unidades\' | translate}}*</label>\n                    <md-select ng-model=\"requerimento.requerente.advogado.descricaoUnidade\"\n                               name=\"Requerente - unidades\">\n                        <md-option ng-repeat=\"value in unidadesGestoras\" ng-value=\"value.descricao\">\n                            {{value.descricao}}\n                        </md-option>\n                    </md-select>\n                </md-input-container>\n            </div>\n            <dados-representante nome-campo=\"\'Representante Legal\'\"\n                                 representante-legal=\"requerimento.requerente\"\n                                 obrigatorio=\"true\"></dados-representante>\n        </div>\n        --> <span class=\"texto-verde detalhar-label\">{{\'dados-imovel\' | translate}}</span><div><detalhar-imovel requerimento=requerimento tipo=\"\'NOVA_CONSULTA_DOMINIALIDADE\'\"></detalhar-imovel></div><span class=\"texto-verde detalhar-label\">{{\"dados-documentos\" | translate | uppercase}}</span><div><detalhar-documento flex=100 id-servico=requerimento.idServico id-tipo-solicitante=requerimento.requerente.tipoSolicitante.id tipo-solicitante=requerimento.requerente.tipoSolicitante.tipo id-requerimento=requerimento.id></detalhar-documento></div><div layout=row ng-if=requerimento.prioridadeAtendimento><detalhar-prioridade-atendimento flex=100 bloquear=true requerimento=requerimento></detalhar-prioridade-atendimento></div></div><acoes-requerimento-servico requerimento=requerimento></acoes-requerimento-servico></div>");
$templateCache.put("scripts-servicos/pages/servicos/view/partials/arquivosAjuda.html","<md-dialog id=su-servicos aria-label=\"{{\'arquivo-ajuda\' | translate}}\" flex=60 ng-cloak><form name=formArquivosAjuda ng-submit=adicionar() novalidate><md-toolbar><div class=md-toolbar-tools><h2>{{\'arquivo-ajuda\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row class=top-2><md-input-container class=md-block flex=100><label>{{\'nome-campo\' | translate}}</label><input ng-model=arquivoAjuda.nomeCampo md-maxlength=100 maxlength=100 name=\"{{\'nome-campo\' | translate}}\" ng-required=true></md-input-container></div><div layout=row><md-input-container flex=50><label>{{arquivoAjuda.arquivos[0].nome}}</label><input ng-model=arquivos ng-required=\"arquivoAjuda.arquivos.length == 0\" name=\"{{\'upload-ajuda\' | translate}}\" disabled></md-input-container><md-button ngf-select=\"addDocumentos($files, arquivoAjuda.arquivos)\" ngf-multiple=true class=md-raised style=\"height: 36px\" ng-if=\"arquivoAjuda.arquivos.length == 0\">{{\'selecionar-arquivo\' | translate}}</md-button><div layout=row ng-if=\"arquivoAjuda.arquivos.length > 0\" ng-repeat=\"arquivo in arquivoAjuda.arquivos\" class=bottom-1><a href=\"\" ng-click=\"removerArquivo($event, arquivo)\" style=\"padding-top: 10px\"><ng-md-icon icon=delete style=fill:#788489></ng-md-icon></a></div></div><div layout=row layout-align=end><md-button type=submit layout-align=right class=\"md-raised alinhamento-dir\">{{(!editar) ? \'btn-inserir\' : \'btn-alterar\' | translate | uppercase}}</md-button></div></div></md-dialog-content></form><div layout=row ng-if=\"listaArquivosAjuda.length > 0\" class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column></th><th md-column class=texto-centro>{{\'nome-campo\' | translate}}</th><th md-column class=texto-centro>{{\'nome-arquivo\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in listaArquivosAjuda | limitTo: queryDados.limit: (queryDados.page - 1) * queryDados.limit track by $index\"><td md-cell md-cell class=texto-centro-corpo style=\"width: 1px; padding: 0 !important\"><p style=\"margin: 0\"><md-button class=\"material-icons botao-posicao\" ng-click=subirPosicao($index)>keyboard_arrow_up</md-button><md-button class=\"material-icons botao-posicao\" ng-click=descerPosicao($index)>keyboard_arrow_down</md-button></p></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.nomeCampo}}</p></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.arquivos[0].nome}}</p></td><td md-cell class=\"texto-centro-corpo padding-right-0\"><a href=\"\" ng-click=editarDados(item) style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"{{\'editar-dados\' | translate}}\"><md-tooltip>{{\'editar-dados\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=removerDados(item); style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'excluir-dados\' | translate}}\"><md-tooltip>{{\'excluir-dados\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listaArquivosAjuda.length > 0\" md-limit=queryDados.limit md-limit-options=queryDados.limitsPage md-page=queryDados.page md-total={{listaArquivosAjuda.length}} md-page-select=true></md-table-pagination></md-content></div><div layout=row layout-sm=column ng-if=\"listaArquivosAjuda.length>0\" layout-align=end class=top-2><md-button class=md-raised ng-click=confirmar()>Confirmar</md-button></div></md-dialog>");
$templateCache.put("scripts-servicos/pages/servicos/view/partials/dadosComplementares.html","<md-dialog id=su-servicos aria-label=\"{{\'dados-complementares\' | translate}}\" flex=60 ng-cloak><form name=formDadosComplementares ng-submit=adicionar() novalidate><md-toolbar><div class=md-toolbar-tools><h2>{{\'dados-complementares\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row><md-input-container class=md-block flex=50><label>{{\'tipo-campo\' | translate}}</label><md-select name=tipo-campo ng-required=true ng-change=alterarTipoCampo() ng-model=dadoComplementar.tipoDadoComplementar ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option ng-repeat=\"item in tipoDadosComplementar\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><md-input-container ng-if=\"isCombo() || isComponente()\" class=md-block flex=50><label>{{ isCombo() ? \'combos\' : \'componentes\'| translate}}</label><md-select name=combo-componente ng-required=true ng-model=dadoComplementar.comboCompoenenteSelecionando ng-change=selecionarCombo()><md-option ng-selected=\"dadoComplementar.comboCompoenenteSelecionando.nomeCampo === item.nomeCampo\" ng-repeat=\"item in valoresCompoComponentes\" ng-value=item>{{item.nomeCampo}}</md-option></md-select></md-input-container><md-input-container ng-if=isComboUf() class=md-block flex=50><label>{{\'label-uf-combo\' | translate}}</label><md-select name=tipo-campo ng-required=true ng-change=selecionarCombo() ng-model=dadoComplementar.comboCompoenenteSelecionando multiple><md-option ng-repeat=\" uf in listUfs | orderBy\" ng-value=uf ng-selected=true ng-disabled=true>{{uf}}</md-option></md-select></md-input-container></div><div layout=row class=top-2><md-input-container class=md-block flex=100><label>{{\'nome-campo\' | translate}}</label><input ng-model=dadoComplementar.nomeCampo md-maxlength=100 maxlength=100 ng-disabled=\"isComponente() && !isNovaCombo()\" name=\"{{\'nome-campo\' | translate}}\" ng-required=true></md-input-container></div><div layout=row ng-if=isCombo()><md-input-container flex><label>{{\'valor-combo\' | translate}}*<md-tooltip>{{\'pressione-enter\' | translate}}</md-tooltip></label><md-chips md-on-add=addChips($chip) md-on-remove=removeChips($chip) ng-model=listaChips ng-required=true readonly name=\"{{\'valor-combo\' | translate}}\" delete-button-label=\"{{\'label-delete-button-palavra-chave\' | translate}}\" delete-hint=\"{{\'delete-hint-palavra-chave\' | translate}}\" secondary-placeholder=\"{{\'mais-incluir-valor\' | translate}}\" placeholder=\"Pressione Ente\n                          {{dadosComplementares.tipoDadoComplementar.id}}r\"></md-chips></md-input-container></div><div layout=row><md-input-container flex=50><label>{{\'upload-ajuda\' | translate}}</label><input ng-model=arquivos name=\"{{\'upload-ajuda\' | translate}}\" disabled></md-input-container><md-button ngf-select=\"addDocumentos($files, dadoComplementar.arquivosAjuda)\" ngf-multiple=true class=md-raised style=\"height: 36px\">{{\'selecionar-arquivo\' | translate}}</md-button></div><div layout=row ng-if=\"dadoComplementar.arquivosAjuda.length > 0\" ng-repeat=\"arquivo in dadoComplementar.arquivosAjuda\" class=bottom-1><div style=\"padding-left: 5px\">{{arquivo.nome}}</div><a href=\"\" ng-click=\"removerArquivo($event, arquivo)\"><ng-md-icon icon=delete style=fill:#788489></ng-md-icon></a></div><div layout=row><md-input-container flex=50><md-checkbox ng-model=possuiDependencia class=md-primary ng-click=removeObrigatoriedade()>Possui dependência com algum campo do tipo combo</md-checkbox></md-input-container><md-input-container flex=50 ng-if=possuiDependencia><label>Campo Combo</label><md-select ng-required=true ng-model=dadoComplementar.campoDependente><md-option ng-value=undefined>Selecione</md-option><md-option ng-repeat=\"item in listaCombos\" ng-value=item>{{item.nomeCampo}}</md-option></md-select></md-input-container></div><div layout=row layout-align=\"end end\" ng-if=dadoComplementar.campoDependente><md-input-container flex=50><label>Valor Campo</label><md-select ng-required=true ng-model=dadoComplementar.valorDependente><md-option ng-value=undefined>Selecione</md-option><md-option ng-repeat=\"item in dadoComplementar.campoDependente.valorComboDadoComplementar\" ng-value=item.descricao>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row><md-input-container class=md-block flex=50><label>{{\'obrigatoriedade\' | translate}}</label><md-select name=obrigatoriedade ng-required=true ng-model=dadoComplementar.obrigatoriedade ng-if=!possuiDependencia><md-option ng-repeat=\"item in valoresObrigatoriedade\" ng-value=item>{{item}}</md-option></md-select></md-input-container><div layout=column ng-if=\"dadoComplementar.tipoDadoComplementar && isNumericoOuTexto()\" flex=50><md-input-container class=md-block><label>{{\'tamanho-campo\' | translate}}</label><input ng-model=dadoComplementar.tamanhoCampo type=number value={{dadoComplementar.tamanhoCampo}} name=\"{{\'tamanho-campo\' | translate}}\" ng-required=isNumericoOuTexto()></md-input-container><label ng-if=isNumeroDecimal()>Ex: {{dadoComplementar.tamanhoCampo | number:2}}</label></div></div><div layout=row layout-align=end><md-button type=submit layout-align=right class=\"md-raised alinhamento-dir\">{{(!editar) ? \'btn-inserir\' : \'btn-alterar\' | translate | uppercase}}</md-button></div></div></md-dialog-content></form><div layout=row ng-if=\"listaDadosComplementar.length > 0\" class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column></th><th md-column class=texto-centro>{{\'nome-campo\' | translate}}</th><th md-column class=texto-centro>{{\'tipo-campo\' | translate}}</th><th md-column class=texto-centro>{{\'tamanho-campo\' | translate}}</th><th md-column class=texto-centro>{{\'obrigatoriedade\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in listaDadosComplementar | limitTo: queryDados.limit: (queryDados.page - 1) * queryDados.limit track by $index\"><td md-cell md-cell class=texto-centro-corpo style=\"width: 1px; padding: 0 !important\"><p style=\"margin: 0\"><md-button class=\"material-icons botao-posicao\" ng-click=subirPosicaoDadoComplementar($index)>keyboard_arrow_up</md-button><md-button class=\"material-icons botao-posicao\" ng-click=descerPosicaoDadoComplementar($index)>keyboard_arrow_down</md-button></p></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.nomeCampo}}</p></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.tipoDadoComplementar.descricao}}</p></td><td md-cell md-cell class=texto-centro-corpo><p>{{isNumeroInteiro(item.tamanhoCampo) ? item.tamanhoCampo : \'n-a\' | translate }}</p></td><td md-cell md-cell><p>{{item.obrigatoriedade}}</p></td><td md-cell class=\"texto-centro-corpo padding-right-0\"><a href=\"\" ng-click=editarDados(item) style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"{{\'editar-dados\' | translate}}\"><md-tooltip>{{\'editar-dados\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=removerDados(item); style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'excluir-dados\' | translate}}\"><md-tooltip>{{\'excluir-dados\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"listaDadosComplementar.length > 0\" md-limit=queryDados.limit md-limit-options=queryDados.limitsPage md-page=queryDados.page md-total={{listaDadosComplementar.length}} md-page-select=true></md-table-pagination></md-content></div><div layout=row layout-sm=column ng-if=\"listaDadosComplementar.length>0\" layout-align=end class=top-2><md-button class=md-raised ng-click=confirmar()>Confirmar</md-button></div></md-dialog>");
$templateCache.put("scripts-servicos/pages/servicos/view/partials/declaracoes.html","<md-dialog id=su-servicos aria-label=\"{{\'label-declaracao\' | translate}}\" flex=60 ng-cloak><form name=formDeclaracao ng-submit=\"validar() && addItemDeclaracao()\" novalidate><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-declaracao\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-margin><md-input-container class=md-block flex=50><label>{{\'label-solicitante-obrigatorio\' | translate}}</label><md-select name=label-solicitante-obrigatorio ng-required=true ng-model=novoItemDeclaracao.solicitantes multiple><md-option ng-repeat=\"item in solicitantes\" ng-value=item>{{item.tipo}}</md-option></md-select></md-input-container></div><label style=\"padding-left: 18px\">{{\'label-declaracao\' | translate}}:</label><div layout=row layout-margin><md-input-container flex=100 class=\"textangular campoDeclaracao\" id=campoDeclaracao><text-angular servico-trim ng-model=novoItemDeclaracao.item ta-max-text=400 md-select-on-focus></text-angular></md-input-container></div><div layout=row class=lef-1 layout-align=end><md-button id=incluirDeclaracao type=submit layout-align=right class=\"md-raised alinhamento-dir declaracaoIncluir\">{{\'btn-incluir\' | translate}}</md-button></div><div layout=row ng-if=\"declaracoes.length>0\" class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro></th><th md-column class=texto-centro>{{\'label-item\' | translate}}</th><th md-column class=texto-centro>{{\'label-solicitante\' | translate}}</th><th md-column class=texto-centro style=\"padding: 0 15px\">{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in declaracoes | limitTo: queryDeclaracao.limit: (queryDeclaracao.page - 1) * queryDeclaracao.limit\"><td md-cell class=celula><div flex=10><i class=\"material-icons hand\" ng-click=\"ordemDeclaracao($index, $index-1, declaracoes)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemDeclaracao($index, $index+1, declaracoes)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell md-cell class=texto-centro-corpo><p ng-bind-html=\"item.item | to_trusted\"></p></td><td md-cell class=texto-centro-corpo><p ng-repeat=\"i in item.solicitantes\" ng-bind=i.tipo>{{i.tipo}}</p></td><td md-cell class=celula><a href=\"\" ng-click=removerDeclaracao($event,$index); style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'toltip-excluir-declaracao\' | translate}}\"><md-tooltip>{{\'toltip-excluir-declaracao\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table><md-table-pagination ng-show=\"declaracoes.length > 0\" md-limit=queryDeclaracao.limit md-limit-options=queryDeclaracao.limitsPage md-page=queryDeclaracao.page md-total={{declaracoes.length}} md-page-select=true></md-table-pagination></md-table-container></md-content></div><div layout=row layout-sm=column ng-if=\"declaracoes.length>0\" layout-align=\"end center\" class=top-2><md-button class=md-raised ng-click=confirmarDeclaracoes()>{{\'btn-confirmar\' | translate}}</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/pages/servicos/view/partials/documento.html","<md-dialog id=su-servicos aria-label=\"Adicionar Solicitante\" flex=60 ng-cloak><form name=adicionarDocumentos><md-toolbar><div class=md-toolbar-tools><h2>{{(!edit) ? \'btn-adiconar-documento\' : \'btn-editar-documento\' | translate | uppercase}}</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><md-icon aria-label=\"{{\'btn-fechar\' | translate}}\">close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-margin><md-input-container md-is-error=\"adicionarDocumentos.nome.$invalid && adicionarDocumentos.$submitted\" flex=100><label>{{\'label-nome-documento\' | translate}}</label><input ng-model=novoDocumento.nome type=text ng-required=true name=nome></md-input-container></div><div layout=row layout-margin><md-input-container flex=50><label>{{\'label-situacao\' | translate}}</label><md-select ng-required=true ng-model=novoDocumento.tipo><md-option ng-repeat=\"tiposDoc in tiposDocumento\" ng-value=tiposDoc.chave ng-model-options=\"{trackBy: \'$value.chave\'}\">{{tiposDoc.valor}}</md-option></md-select></md-input-container><md-input-container flex=50><label>{{\'label-solicitante-obrigatorio\' | translate}}</label><md-select ng-required=true ng-model=novoDocumento.solicitantes multiple ng-model-options=\"{trackBy: \'$value.indice\'}\"><md-option ng-repeat=\"item in solicitantes\" ng-value=item>{{item.tipo}}</md-option></md-select></md-input-container></div><div layout=row layout-margin><md-input-container flex=50><label>{{\'label-tipo-documento\' | translate}}</label><md-select ng-model=novoDocumento.tipoDocumento.id ng-required=true><md-option ng-repeat=\"listaTiposDoc in listaTiposDocumento\" ng-value=listaTiposDoc.id ng-model-options=\"{trackBy: \'$value.id\'}\">{{listaTiposDoc.nome}}</md-option></md-select></md-input-container></div><div layout-margin=10><label>{{\'label-extensao\' | translate}}</label></div><div layout=row flex-offset=5 layout-wrap layout-aling=\"center center\"><div flex=20 ng-repeat=\"formatoArq in listaFormatosArquivos\"><md-checkbox class=md-primary ng-model=formatoArq.selecionado>{{ \'.\' + (formatoArq.formato | lowercase) }}</md-checkbox></div><div flex=20><md-checkbox class=md-primary ng-model=statusExtensaoDocumento>{{ \'Outro\' }}</md-checkbox></div></div><div layout=row layout-margin=10 ng-show=statusExtensaoDocumento><md-input-container flex=30><label>{{\'extensao\' | translate}}:</label><input ng-model=outraExtensaoDocumento type=text ng-required=statusExtensaoDocumento ng-disabled=!statusExtensaoDocumento maxlength=10></md-input-container></div><div layout=row layout-align=end><md-button type=submit layout-align=right class=\"md-raised alinhamento-dir\" ng-click=addDocumento($event)>{{(!edit) ? \'btn-inserir\' : \'btn-alterar\' | translate | uppercase}}</md-button></div><div layout=row ng-show=\"documentos.length > 0\" class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro></th><th md-column class=texto-centro>{{\'label-nome-documento\' | translate}}</th><th md-column class=texto-centro>{{\'label-solicitante\' | translate}}</th><th md-column class=texto-centro>{{\'label-situacao\' | translate}}</th><th md-column class=texto-centro>{{\'label-tipo-documento\' | translate}}</th><th md-column class=texto-centro>{{\'label-extensao\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in documentos | limitTo: queryDocumento.limit: (queryDocumento.page - 1) * queryDocumento.limit\"><td md-cell md-cell class=texto-centro-corpo><div flex=10><i class=\"material-icons hand\" ng-click=\"ordemDocumento($index, $index-1, documentos)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemDocumento($index, $index+1, documentos)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.nome}}</p></td><td md-cell md-cell class=texto-centro-corpo><p ng-repeat=\"i in item.solicitantes\" ng-bind=i.tipo>{{i.requisitos}}</p></td><td md-cell md-cell class=texto-centro-corpo><p ng-repeat=\"tipoDoc in tiposDocumento\" ng-if=\"tipoDoc.chave==item.tipo\">{{tipoDoc.valor}}</p></td><td md-cell md-cell class=texto-centro-corpo><p ng-repeat=\"listatiposDoc in listaTiposDocumento\" ng-if=\"listatiposDoc.id==item.tipoDocumento.id\">{{listatiposDoc.nome}}</p></td><td md-cell md-cell class=texto-centro-corpo><p ng-repeat=\"formato in item.formatoArquivos\">{{ \'.\' + (formato.formato | lowercase) }}</p></td><td md-cell class=texto-centro-corpo><a href=\"\" ng-click=\"editar($event, item)\" style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"{{\'toltip-editar-documento\' | translate}}\"><md-tooltip>{{\'toltip-editar-documento\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=\"remover($event, item);\" style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'toltip-excluir-documento\' | translate}}\"><md-tooltip>{{\'toltip-excluir-documento\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"documentos.length > 0\" md-limit=queryDocumento.limit md-limit-options=queryDocumento.limitsPage md-page=queryDocumento.page md-total={{documentos.length}} md-page-select=true></md-table-pagination></md-content></div><div layout=row ng-if=\"documentos.length > 0\" layout-sm=column layout-align=\"end center\" class=top-2><md-button class=md-raised ng-click=confirmar()>{{\'btn-confirmar\' | translate | uppercase}}</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/pages/servicos/view/partials/solicitante.html","<md-dialog id=su-servicos aria-label=\"Adicionar Solicitante\" flex=60 ng-cloak><form name=adicionarSolicitantes><md-toolbar><div class=md-toolbar-tools><h2 ng-if=!edit>Adicionar Solicitante</h2><h2 ng-if=edit>Editar Solicitante</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-margin class=diferentao><md-input-container md-is-error=adicionarSolicitantes.name.$invalid class=md-block flex=100><label>{{\'label-tipo-obrigatorio\' | translate}}</label><input ng-model=novoSolicitante.tipo type=text ng-required=true name=tipo></md-input-container></div><div layout=row layout-margin><md-input-container class=md-block flex=100><label>{{\'label-segmento\' | translate}}</label><md-select ng-model=novoSolicitante.segmento multiple ng-model-options=\"{trackBy: \'$value.id\'}\"><md-option ng-repeat=\"item in tipoSegmento\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-margin><md-input-container flex=100 md-is-error=\"adicionarSolicitantes.$error.minlength || adicionarSolicitantes.$error.maxlength\"><label>{{\'label-requisito-obrigatorio\' | translate}}</label><textarea name=bio ng-model=novoSolicitante.requisitos ng-required=true ng-minlength=10 ng-maxlength=300 maxlength=300 md-maxlength=300></textarea></md-input-container></div><div layout=row layout-align=end><md-button type=submit class=\"md-raised alinhamento-dir\" ng-click=addSolicitante($event)>{{ (edit) ? \'btn-alterar\' : \'btn-inserir\' | translate | uppercase }}</md-button></div><div layout=row ng-show=\"solicitantes.length>0\" class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>Tipo</th><th md-column class=texto-centro>{{\'label-segmentos\' | translate}}</th><th md-column class=texto-centro>{{\'label-requisitos\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in solicitantes | orderBy: querySolicitante.order | limitTo: querySolicitante.limit: (querySolicitante.page - 1) * querySolicitante.limit\"><td md-cell class=celula>{{item.tipo}}</td><td md-cell class=celula><p ng-repeat=\"i in item.segmento\" ng-bind=i.descricao></p></td><td md-cell class=celula>{{item.requisitos}}</td><td md-cell class=celula><a href=\"\" ng-click=\"editar($event, item)\" style=\"text-decoration: none\"><ng-md-icon icon=create style=fill:#788489 size=22 alt=\"{{\'toltip-editar-solicitante\' | translate}}\"><md-tooltip>{{\'toltip-editar-solicitante\' | translate}}</md-tooltip></ng-md-icon></a><a href=\"\" ng-click=\"remover($event, item);\" style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'toltip-excluir-solicitante\' | translate}}\"><md-tooltip>{{\'toltip-excluir-solicitante\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table><md-table-pagination ng-show=\"solicitantes.length > 0\" md-limit=querySolicitante.limit md-limit-options=querySolicitante.limitsPage md-page=querySolicitante.page md-total={{solicitantes.length}} md-page-select=true></md-table-pagination></md-table-container></md-content></div><div layout=row layout-sm=column ng-if=\"solicitantes.length>0\" layout-align=end class=top-2><md-button class=md-raised ng-click=confirmar()>Confirmar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/pages/servicos/view/partials/subsecoes.html","<md-dialog id=su-servicos aria-label=\"{{\'adicionar-subsecao\' | translate}}\" flex=60 ng-cloak><form name=formSubsecoes ng-submit=adicionar() novalidate><md-toolbar><div class=md-toolbar-tools><h2>{{\'adicionar-subsecao\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=column class=top-2><md-input-container class=md-block flex=100><label>{{\'nome-subsecao\' | translate}}</label><input ng-model=subsecao.nome md-maxlength=100 maxlength=100 name=\"{{\'nome-subsecao\' | translate}}\" required></md-input-container><div layout=row><md-input-container class=md-block flex=50><label>{{\'campo-subsecao\' | translate}} :</label><md-select ng-model=dadoComplementar required><md-option ng-repeat=\"dado in dadosComplementares\" ng-value=dado ng-if=!dado.hasSubsecao>{{dado.nomeCampo}}</md-option></md-select></md-input-container><div layout=row flex=30><md-button type=submit layout-align=right class=\"md-raised alinhamento-dir\">{{\'btn-inserir\' | translate | uppercase}}</md-button></div></div></div></div></md-dialog-content></form><div layout=row ng-if=\"subsecao.campos.length > 0\" class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'nome-campo\' | translate}}</th><th md-column class=texto-centro>{{\'tamanho-campo\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in subsecao.campos | limitTo: queryDados.limit: (queryDados.page - 1) * queryDados.limit\" ng-if=item.status><td md-cell md-cell class=texto-centro-corpo><p>{{item.nomeCampo}}</p></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.tamanhoCampo ? item.tamanhoCampo : \'n-a\' | translate }}</p></td><td md-cell class=\"texto-centro-corpo padding-right-0\"><a href=\"\" ng-click=\"removerCampo($event, item);\" style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt=\"{{\'excluir-dados\' | translate}}\"><md-tooltip>{{\'excluir-dados\' | translate}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table></md-table-container><md-table-pagination ng-show=\"subsecao.campos.length > 0\" md-limit=queryDados.limit md-limit-options=queryDados.limitsPage md-page=queryDados.page md-total={{subsecao.campos.length}} md-page-select=true></md-table-pagination></md-content></div><div layout=row layout-sm=column ng-if=\"subsecao.campos.length>0\" layout-align=end class=top-2><md-button class=md-raised ng-click=confirmar()>Confirmar</md-button></div></md-dialog>");
$templateCache.put("scripts-servicos/pages/servicos/view/partials/suspender.html","<md-dialog id=su-servicos aria-label=Suspender flex=60 ng-cloak><form name=adicionarSolicitantes><md-toolbar><div class=\"md-toolbar-tools text-center\"><h2>Suspender</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row layout-margin><md-input-container class=md-block><label>Suspender</label><textarea ng-model=servico.justificativa maxlength=200 required name=suspender md-maxlength=200></textarea></md-input-container></div><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=confirmar() class=md-raised>Confirmar</md-button><md-button ng-click=closeDialog() class=md-raised>Cancelar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/pages/triagem/view/partials/anotacoes.html","<md-dialog id=su-servicos aria-label=Suspender flex=60 ng-cloak><form name=adicionarSolicitantes><md-toolbar><div class=\"md-toolbar-tools text-center\"><h2>{{\'label-anotacoes\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row flex=100 layout-margin><md-input-container flex=100 class=md-block><label>{{\'label-anotacoes\' | translate}}</label><textarea ng-model=requerimento.anotacoes ng-required=true name=anotacoes maxlength=500 md-maxlength=500></textarea></md-input-container></div><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=confirmar() class=md-raised>Salvar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/pages/triagem/view/partials/cancelarEnvioAutomaticoSei.html","<md-dialog id=su-servicos aria-label=Suspender flex=60 ng-cloak><form name=adicionarSolicitantes><md-toolbar><div class=\"md-toolbar-tools text-center\"><h2>Cancelar a rotina de envio de processos ao SEI</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div layout=row flex=100 layout-margin>Deseja cancelar o envio automático?</div><div layout=row flex=50 layout-margin><md-input-container flex=100 class=md-block><label>Informe o Nº do processo:</label><input ng-model=requerimento.numeroProcesso ui-mask=99999.999999/9999-99 ng-required=true name=numeroProcesso></md-input-container></div><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=fechar() class=md-raised>Não</md-button><md-button ng-click=confirmar() class=md-raised>Sim</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/pages/triagem/view/partials/pausarAtendimento.html","<md-dialog id=su-servicos aria-label=Suspender flex=40 ng-cloak><form name=adicionarSolicitantes><md-toolbar><div class=\"md-toolbar-tools text-center\"><h2>{{\'label-anotacoes\' | translate}}</h2><span flex></span><md-button class=md-icon-button ng-click=fechar()><md-icon md-svg-src=assets/img/icons/ic_close_24px.svg aria-label=Fechar></md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content>Por quanto tempo o atendimento será interrompido?<div layout=column flex=100 layout-margin><md-input-container flex=100 class=md-block><label>Quantidade dias</label><input type=number ng-model=requerimento.numeroDias ng-required=true></md-input-container><md-input-container flex=100 class=md-block><label>{{\'label-justificativa\' | translate}}</label><textarea ng-model=requerimento.justificativa ng-required=true name=anotacoes maxlength=500 md-maxlength=500></textarea></md-input-container></div><div layout=row flex=100 layout-align=\"end center\"><md-button ng-click=confirmar() class=md-raised>Salvar</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/utilitarios/templates/partials/modalAddChecklist.html","<md-dialog id=su-servicos aria-label={{nomeChecklist}} flex=60 ng-cloak><form name=formularioAnalise ng-submit=\"validar() && addItemChecklist()\" novalidate><md-toolbar><div class=md-toolbar-tools><h2>{{nomeChecklist}}</h2><span flex></span><md-button class=md-icon-button ng-click=closeDialog()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><label style=\"padding-left: 18px\">{{\'label-item-checklist-validacao-requerimento\' | translate}}:</label><div layout=row layout-margin class=checklist-analise><md-input-container flex=100 class=textangular><div text-angular servico-trim ng-model=novoItemChecklist.item ta-max-text=200 name=analise></div></md-input-container></div><div layout=row layout-margin class=marcacao><md-checkbox ng-model=novoItemChecklist.obrigatorio ng-selected=novoItemChecklist.obrigatorio aria-label=\"{{\'marcacao-obrigatoria\' | translate}}\" class=md-primary>{{\'marcacao-obrigatoria\' | translate}}</md-checkbox></div><div layout=row class=lef-1 layout-align=end><md-button type=submit class=\"md-raised alinhamento-dir\" class=md-raised>{{\'btn-incluir\' | translate}}</md-button></div><div layout=row ng-if=\"modalChecklists.length>0\" class=align-tabela><md-content flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro></th><th md-column class=texto-centro>{{\'label-item\' | translate}}</th><th md-column class=texto-centro>{{\'label-obrigatorio\' | translate}}</th><th md-column class=texto-centro>{{\'label-acoes\' | translate}}</th></tr></thead><tbody md-body><tr md-row md-select=item md-select-id=id md-auto-select ng-repeat=\"item in modalChecklists | limitTo: queryChecklist.limit: (queryChecklist.page - 1) * queryChecklist.limit\"><td md-cell class=celula><div flex=10><i class=\"material-icons hand\" ng-click=\"ordemChecklist($index, $index-1, modalChecklists)\" ng-if=!$first>keyboard_arrow_up</i> <i class=\"material-icons hand\" ng-click=\"ordemChecklist($index, $index+1, modalChecklists)\" ng-if=!$last>keyboard_arrow_down</i></div></td><td md-cell md-cell class=texto-centro-corpo><p ng-bind-html=\"item.item | to_trusted\"></p></td><td md-cell md-cell class=texto-centro-corpo><p>{{item.obrigatorio ? \'sim\' : \'nao\' | translate}}</p></td><td md-cell class=celula><a href=\"\" ng-click=removerChecklist($event,$index); style=\"text-decoration: none\"><ng-md-icon icon=delete style=fill:#788489 size=22 alt={{labelExcluirItemChecklist}}><md-tooltip>{{labelExcluirItemChecklist}}</md-tooltip></ng-md-icon></a></td></tr></tbody></table><md-table-pagination ng-show=\"modalChecklists.length > 0\" md-limit=queryChecklist.limit md-limit-options=queryChecklist.limitsPage md-page=queryChecklist.page md-total={{modalChecklists.length}} md-page-select=true></md-table-pagination></md-table-container></md-content></div><div layout=row ng-if=\"modalChecklists.length>0\" layout-sm=column layout-align=\"end center\" class=top-2><md-button class=md-raised ng-click=confirmarChecklist()>{{\'btn-confirmar\' | translate}}</md-button></div></div></md-dialog-content></form></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/utilitarios/templates/partials/modalExibirMensagemErroSei.html","<md-dialog ng-cloak id=su-servicos><md-toast></md-toast><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-atencao\' | translate}}</h2><span flex></span><md-button ng-click=fechar()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><div layout=column><label class=modalAnaliseTecnica>Processo de triagem concluído com sucesso. SEI indisponível no momento.<br>O sistema continuará tentando o envio automático, mas você poderá cancelar o envio. Acesse a opção \"Cancelar envio automático SEI\", no menu ações da página inicial e insira o nº do processo manualmente.<br><br><b>Motivo:</b><br><pre>{{mensagemErro}}</pre><p ng-if=\"arquivosErro && arquivosErro.length > 0\"><b>Arquivos com erro:</b><ul><li ng-repeat=\"arquivo in arquivosErro\">{{arquivo.nomeArquivo}}</li></ul></p></label><div layout=row layout-align=\"end end\"><md-button class=md-raised ng-click=fechar() id=botaoFechar><label>{{\'btn-fechar\' | translate}}</label></md-button><!--          <md-button class=\"md-raised\" ng-click=\"voltarParaAnaliseTecnica()\">\n            <label>{{\'confirmacao\' | translate}}</label>\n          </md-button>--></div></div></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/utilitarios/templates/partials/modalExibirSEI.html","<md-dialog ng-cloak id=su-servicos><md-toast></md-toast><md-toolbar><div class=md-toolbar-tools><h2>{{\'label-confirmacao\' | translate}}</h2><span flex></span><md-button ng-click=fechar()><md-icon aria-label=Fechar>close</md-icon></md-button></div></md-toolbar><div layout-margin=50 style=\"width: 305px\"><div layout=column><label class=modalAnaliseTecnica>Registro salvo com sucesso.<br><br><span ng-show=\"(procedimentoSEI != undefined && statusRequerimento == \'EM_ANALISE_TECNICA\')\">O número do processo no SEI é <b>{{procedimentoSEI}}</b></span></label><div layout=row layout-align=\"end end\"><md-button class=md-raised ng-click=fechar()><label>{{\'fechar\' | translate}}</label></md-button><!--          <md-button class=\"md-raised\" ng-click=\"voltarParaAnaliseTecnica()\">\n            <label>{{\'confirmacao\' | translate}}</label>\n          </md-button>--></div></div></div></md-dialog>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/imovel/detalharImovelDadosUtilizacao.html","<div layout=row layout-margin=5 layout-xs=column><label class=detalhar-label>{{\'label-planta\' | translate}}:</label><div layout=row ng-repeat=\"planta in requerimento.imovel.plantas\"><a href=#>{{planta.nome}}</a></div></div><div layout=column layout-margin=5 layout-xs=column ng-show=verificarTipo();><div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'tipo-imovel\' | translate}}:</label><label ng-bind=requerimento.imovel.tipoImovel></label></div><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'area-terreno\' | translate}}:</label><label ng-bind=requerimento.imovel.areaTerreno></label></div><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'area-construida\' | translate}}:</label><label ng-bind=requerimento.imovel.areaConstruida></label></div></div><div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5><fotos-imovel flex=100 imovel=requerimento.imovel visualizar=true></fotos-imovel></div></div><div><div layout=row layout-xs=column style=\"margin-top: 2%\"><div ng-show=\"requerimento.tipoRequerimento == \'REVISAO_VALOR_AREA\'\"><md-container class=\"infoSocioEconomica table-responsive-vertical\"><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'label-dados-utilizacao\' | translate | uppercase}}</th><th md-column class=texto-centro>{{\'label-deseja-revisar\' | translate | uppercase}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"uso in requerimento.imovel.usos\"><td md-cell style=\"text-align: left !important\"><div class=row style=\"margin-top: 1%\"><label>{{\'label-instrumento-destinacao\' | translate}}:</label><label class=negrito>{{uso.instrumentoDestinacao ? uso.instrumentoDestinacao : \'-\'}}</label></div><div class=row style=\"margin-top: 1%\"><label>{{\'label-vigencia\' | translate}}:</label><label class=negrito>{{uso.dataVigencia ? uso.dataVigencia : \'-\' | date:\'dd/MM/yyyy\'}}</label></div></td><td md-cell style=\"text-align: left !important\"><div layout=column flex-xs flex=100 style=\"margin-top: 2%\"><div layout=row flex-xs flex=100><md-checkbox ng-disabled=true ng-model=uso.valorCobranca style=\"width: 100%; margin-bottom: 1%\">{{\'cbx-valor-cobranca\' | translate}}</md-checkbox></div><div flex-xs flex=100><md-checkbox ng-disabled=true ng-model=uso.areaFracaoIdeal style=\"width: 100%; margin-bottom: 1%\">{{\'cbx-area-fracao-ideal\' | translate}}</md-checkbox></div></div></td></tr></tbody></table></md-table-container></md-container></div></div><div layout=row layout-margin=5 ng-show=requerimento.imovel.justificativa><div layout=row><label class=detalhar-label>{{\'label-justificativa\' | translate}}:</label></div><div layout=row><label ng-bind=requerimento.imovel.justificativa></label></div></div></div>");
$templateCache.put("scripts-servicos/app/directives/detalhar-requerimento/templates/imovel/detalharImovelTransferenciaTitular.html","<div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'area-terreno\' | translate}}:</label><label ng-bind=requerimento.imovel.areaTerreno></label></div><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'fracao-ideal\' | translate}}:</label><label ng-bind=requerimento.imovel.valorFrancaoIdeal></label></div></div><div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5 ng-show=requerimento.imovel.destinacoes><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'utilizacao-atual\' | translate}}:</label><label>{{getUsoImovelFormatado()}}</label></div></div><div layout=row layout-xs=column flex=25 flex-xs=100 layout-margin=5><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'objetivo-da-transferencia\' | translate}}:</label><label>{{requerimento.imovel.transferenciaImovel.tipoTransferencia == \'TOTAL\' ? \'total\' : \'parcial\' | translate}}</label></div><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'area-fracao-ideal-terreno-transferido\' | translate}}:</label><label>{{requerimento.imovel.transferenciaImovel.areaTerrenoTransferido}}</label></div><div flex=35 flex-xs=100 layout=row><label class=detalhar-label>{{\'imovel-desmembrado\' | translate}}:</label><label>{{requerimento.imovel.transferenciaImovel.imovelDesmembrado ? \'sim\' : \'nao\' | translate}}</label></div></div><!--<div layout=\"row\" layout-xs=\"column\" flex=\"25\" flex-xs=\"100\" layout-margin=\"5\">\n    <div flex=\"35\" flex-xs=\"100\" layout=\"row\">\n      <label class=\"detalhar-label\">{{\'utilizacao-alterada\' | translate}}:</label>\n      <label>{{requerimento.imovel.transferenciaImovel.utilizacaoAlterada ? \'sim\' : \'nao\' | translate}}</label>\n    </div>\n</div>--><div layout=row layout-xs=column ng-show=\"requerimento.imovel.usos.length > 0\"><md-table-container flex=100 flex-xs=100><table md-table><thead md-head><tr md-row><th md-column class=texto-centro>{{\'uso-principal\' | translate | uppercase}}</th><th md-column class=texto-centro>{{\'especificacao-do-uso\' | translate | uppercase}}</th><th md-column class=texto-centro>{{\'descricao-do-uso\' | translate | uppercase}}</th></tr></thead><tbody md-body><tr md-row ng-repeat=\"uso in requerimento.imovel.usos\"><td md-cell style=\"text-align: left !important\"><div class=row style=\"margin-top: 1%\"><label>{{uso.usoPrincipal}}</label></div></td><td md-cell style=\"text-align: left !important\"><div class=row style=\"margin-top: 1%\"><label>{{uso.especificacaoUso}}</label></div></td><td md-cell style=\"text-align: left !important\"><div class=row style=\"margin-top: 1%\"><label>{{uso.descricaoUso}}</label></div></td></tr></tbody></table></md-table-container></div>");}]);
/*!
 * Chart.js v2.9.4
 * https://www.chartjs.org
 * (c) 2020 Chart.js Contributors
 * Released under the MIT License
 */
(function (global, factory) {
typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory(function() { try { return require('moment'); } catch(e) { } }()) :
typeof define === 'function' && define.amd ? define(['require'], function(require) { return factory(function() { try { return require('moment'); } catch(e) { } }()); }) :
(global = global || self, global.Chart = factory(global.moment));
}(this, (function (moment) { 'use strict';

moment = moment && moment.hasOwnProperty('default') ? moment['default'] : moment;

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

function getCjsExportFromNamespace (n) {
	return n && n['default'] || n;
}

var colorName = {
	"aliceblue": [240, 248, 255],
	"antiquewhite": [250, 235, 215],
	"aqua": [0, 255, 255],
	"aquamarine": [127, 255, 212],
	"azure": [240, 255, 255],
	"beige": [245, 245, 220],
	"bisque": [255, 228, 196],
	"black": [0, 0, 0],
	"blanchedalmond": [255, 235, 205],
	"blue": [0, 0, 255],
	"blueviolet": [138, 43, 226],
	"brown": [165, 42, 42],
	"burlywood": [222, 184, 135],
	"cadetblue": [95, 158, 160],
	"chartreuse": [127, 255, 0],
	"chocolate": [210, 105, 30],
	"coral": [255, 127, 80],
	"cornflowerblue": [100, 149, 237],
	"cornsilk": [255, 248, 220],
	"crimson": [220, 20, 60],
	"cyan": [0, 255, 255],
	"darkblue": [0, 0, 139],
	"darkcyan": [0, 139, 139],
	"darkgoldenrod": [184, 134, 11],
	"darkgray": [169, 169, 169],
	"darkgreen": [0, 100, 0],
	"darkgrey": [169, 169, 169],
	"darkkhaki": [189, 183, 107],
	"darkmagenta": [139, 0, 139],
	"darkolivegreen": [85, 107, 47],
	"darkorange": [255, 140, 0],
	"darkorchid": [153, 50, 204],
	"darkred": [139, 0, 0],
	"darksalmon": [233, 150, 122],
	"darkseagreen": [143, 188, 143],
	"darkslateblue": [72, 61, 139],
	"darkslategray": [47, 79, 79],
	"darkslategrey": [47, 79, 79],
	"darkturquoise": [0, 206, 209],
	"darkviolet": [148, 0, 211],
	"deeppink": [255, 20, 147],
	"deepskyblue": [0, 191, 255],
	"dimgray": [105, 105, 105],
	"dimgrey": [105, 105, 105],
	"dodgerblue": [30, 144, 255],
	"firebrick": [178, 34, 34],
	"floralwhite": [255, 250, 240],
	"forestgreen": [34, 139, 34],
	"fuchsia": [255, 0, 255],
	"gainsboro": [220, 220, 220],
	"ghostwhite": [248, 248, 255],
	"gold": [255, 215, 0],
	"goldenrod": [218, 165, 32],
	"gray": [128, 128, 128],
	"green": [0, 128, 0],
	"greenyellow": [173, 255, 47],
	"grey": [128, 128, 128],
	"honeydew": [240, 255, 240],
	"hotpink": [255, 105, 180],
	"indianred": [205, 92, 92],
	"indigo": [75, 0, 130],
	"ivory": [255, 255, 240],
	"khaki": [240, 230, 140],
	"lavender": [230, 230, 250],
	"lavenderblush": [255, 240, 245],
	"lawngreen": [124, 252, 0],
	"lemonchiffon": [255, 250, 205],
	"lightblue": [173, 216, 230],
	"lightcoral": [240, 128, 128],
	"lightcyan": [224, 255, 255],
	"lightgoldenrodyellow": [250, 250, 210],
	"lightgray": [211, 211, 211],
	"lightgreen": [144, 238, 144],
	"lightgrey": [211, 211, 211],
	"lightpink": [255, 182, 193],
	"lightsalmon": [255, 160, 122],
	"lightseagreen": [32, 178, 170],
	"lightskyblue": [135, 206, 250],
	"lightslategray": [119, 136, 153],
	"lightslategrey": [119, 136, 153],
	"lightsteelblue": [176, 196, 222],
	"lightyellow": [255, 255, 224],
	"lime": [0, 255, 0],
	"limegreen": [50, 205, 50],
	"linen": [250, 240, 230],
	"magenta": [255, 0, 255],
	"maroon": [128, 0, 0],
	"mediumaquamarine": [102, 205, 170],
	"mediumblue": [0, 0, 205],
	"mediumorchid": [186, 85, 211],
	"mediumpurple": [147, 112, 219],
	"mediumseagreen": [60, 179, 113],
	"mediumslateblue": [123, 104, 238],
	"mediumspringgreen": [0, 250, 154],
	"mediumturquoise": [72, 209, 204],
	"mediumvioletred": [199, 21, 133],
	"midnightblue": [25, 25, 112],
	"mintcream": [245, 255, 250],
	"mistyrose": [255, 228, 225],
	"moccasin": [255, 228, 181],
	"navajowhite": [255, 222, 173],
	"navy": [0, 0, 128],
	"oldlace": [253, 245, 230],
	"olive": [128, 128, 0],
	"olivedrab": [107, 142, 35],
	"orange": [255, 165, 0],
	"orangered": [255, 69, 0],
	"orchid": [218, 112, 214],
	"palegoldenrod": [238, 232, 170],
	"palegreen": [152, 251, 152],
	"paleturquoise": [175, 238, 238],
	"palevioletred": [219, 112, 147],
	"papayawhip": [255, 239, 213],
	"peachpuff": [255, 218, 185],
	"peru": [205, 133, 63],
	"pink": [255, 192, 203],
	"plum": [221, 160, 221],
	"powderblue": [176, 224, 230],
	"purple": [128, 0, 128],
	"rebeccapurple": [102, 51, 153],
	"red": [255, 0, 0],
	"rosybrown": [188, 143, 143],
	"royalblue": [65, 105, 225],
	"saddlebrown": [139, 69, 19],
	"salmon": [250, 128, 114],
	"sandybrown": [244, 164, 96],
	"seagreen": [46, 139, 87],
	"seashell": [255, 245, 238],
	"sienna": [160, 82, 45],
	"silver": [192, 192, 192],
	"skyblue": [135, 206, 235],
	"slateblue": [106, 90, 205],
	"slategray": [112, 128, 144],
	"slategrey": [112, 128, 144],
	"snow": [255, 250, 250],
	"springgreen": [0, 255, 127],
	"steelblue": [70, 130, 180],
	"tan": [210, 180, 140],
	"teal": [0, 128, 128],
	"thistle": [216, 191, 216],
	"tomato": [255, 99, 71],
	"turquoise": [64, 224, 208],
	"violet": [238, 130, 238],
	"wheat": [245, 222, 179],
	"white": [255, 255, 255],
	"whitesmoke": [245, 245, 245],
	"yellow": [255, 255, 0],
	"yellowgreen": [154, 205, 50]
};

var conversions = createCommonjsModule(function (module) {
/* MIT license */


// NOTE: conversions should only return primitive values (i.e. arrays, or
//       values that give correct `typeof` results).
//       do not use box values types (i.e. Number(), String(), etc.)

var reverseKeywords = {};
for (var key in colorName) {
	if (colorName.hasOwnProperty(key)) {
		reverseKeywords[colorName[key]] = key;
	}
}

var convert = module.exports = {
	rgb: {channels: 3, labels: 'rgb'},
	hsl: {channels: 3, labels: 'hsl'},
	hsv: {channels: 3, labels: 'hsv'},
	hwb: {channels: 3, labels: 'hwb'},
	cmyk: {channels: 4, labels: 'cmyk'},
	xyz: {channels: 3, labels: 'xyz'},
	lab: {channels: 3, labels: 'lab'},
	lch: {channels: 3, labels: 'lch'},
	hex: {channels: 1, labels: ['hex']},
	keyword: {channels: 1, labels: ['keyword']},
	ansi16: {channels: 1, labels: ['ansi16']},
	ansi256: {channels: 1, labels: ['ansi256']},
	hcg: {channels: 3, labels: ['h', 'c', 'g']},
	apple: {channels: 3, labels: ['r16', 'g16', 'b16']},
	gray: {channels: 1, labels: ['gray']}
};

// hide .channels and .labels properties
for (var model in convert) {
	if (convert.hasOwnProperty(model)) {
		if (!('channels' in convert[model])) {
			throw new Error('missing channels property: ' + model);
		}

		if (!('labels' in convert[model])) {
			throw new Error('missing channel labels property: ' + model);
		}

		if (convert[model].labels.length !== convert[model].channels) {
			throw new Error('channel and label counts mismatch: ' + model);
		}

		var channels = convert[model].channels;
		var labels = convert[model].labels;
		delete convert[model].channels;
		delete convert[model].labels;
		Object.defineProperty(convert[model], 'channels', {value: channels});
		Object.defineProperty(convert[model], 'labels', {value: labels});
	}
}

convert.rgb.hsl = function (rgb) {
	var r = rgb[0] / 255;
	var g = rgb[1] / 255;
	var b = rgb[2] / 255;
	var min = Math.min(r, g, b);
	var max = Math.max(r, g, b);
	var delta = max - min;
	var h;
	var s;
	var l;

	if (max === min) {
		h = 0;
	} else if (r === max) {
		h = (g - b) / delta;
	} else if (g === max) {
		h = 2 + (b - r) / delta;
	} else if (b === max) {
		h = 4 + (r - g) / delta;
	}

	h = Math.min(h * 60, 360);

	if (h < 0) {
		h += 360;
	}

	l = (min + max) / 2;

	if (max === min) {
		s = 0;
	} else if (l <= 0.5) {
		s = delta / (max + min);
	} else {
		s = delta / (2 - max - min);
	}

	return [h, s * 100, l * 100];
};

convert.rgb.hsv = function (rgb) {
	var rdif;
	var gdif;
	var bdif;
	var h;
	var s;

	var r = rgb[0] / 255;
	var g = rgb[1] / 255;
	var b = rgb[2] / 255;
	var v = Math.max(r, g, b);
	var diff = v - Math.min(r, g, b);
	var diffc = function (c) {
		return (v - c) / 6 / diff + 1 / 2;
	};

	if (diff === 0) {
		h = s = 0;
	} else {
		s = diff / v;
		rdif = diffc(r);
		gdif = diffc(g);
		bdif = diffc(b);

		if (r === v) {
			h = bdif - gdif;
		} else if (g === v) {
			h = (1 / 3) + rdif - bdif;
		} else if (b === v) {
			h = (2 / 3) + gdif - rdif;
		}
		if (h < 0) {
			h += 1;
		} else if (h > 1) {
			h -= 1;
		}
	}

	return [
		h * 360,
		s * 100,
		v * 100
	];
};

convert.rgb.hwb = function (rgb) {
	var r = rgb[0];
	var g = rgb[1];
	var b = rgb[2];
	var h = convert.rgb.hsl(rgb)[0];
	var w = 1 / 255 * Math.min(r, Math.min(g, b));

	b = 1 - 1 / 255 * Math.max(r, Math.max(g, b));

	return [h, w * 100, b * 100];
};

convert.rgb.cmyk = function (rgb) {
	var r = rgb[0] / 255;
	var g = rgb[1] / 255;
	var b = rgb[2] / 255;
	var c;
	var m;
	var y;
	var k;

	k = Math.min(1 - r, 1 - g, 1 - b);
	c = (1 - r - k) / (1 - k) || 0;
	m = (1 - g - k) / (1 - k) || 0;
	y = (1 - b - k) / (1 - k) || 0;

	return [c * 100, m * 100, y * 100, k * 100];
};

/**
 * See https://en.m.wikipedia.org/wiki/Euclidean_distance#Squared_Euclidean_distance
 * */
function comparativeDistance(x, y) {
	return (
		Math.pow(x[0] - y[0], 2) +
		Math.pow(x[1] - y[1], 2) +
		Math.pow(x[2] - y[2], 2)
	);
}

convert.rgb.keyword = function (rgb) {
	var reversed = reverseKeywords[rgb];
	if (reversed) {
		return reversed;
	}

	var currentClosestDistance = Infinity;
	var currentClosestKeyword;

	for (var keyword in colorName) {
		if (colorName.hasOwnProperty(keyword)) {
			var value = colorName[keyword];

			// Compute comparative distance
			var distance = comparativeDistance(rgb, value);

			// Check if its less, if so set as closest
			if (distance < currentClosestDistance) {
				currentClosestDistance = distance;
				currentClosestKeyword = keyword;
			}
		}
	}

	return currentClosestKeyword;
};

convert.keyword.rgb = function (keyword) {
	return colorName[keyword];
};

convert.rgb.xyz = function (rgb) {
	var r = rgb[0] / 255;
	var g = rgb[1] / 255;
	var b = rgb[2] / 255;

	// assume sRGB
	r = r > 0.04045 ? Math.pow(((r + 0.055) / 1.055), 2.4) : (r / 12.92);
	g = g > 0.04045 ? Math.pow(((g + 0.055) / 1.055), 2.4) : (g / 12.92);
	b = b > 0.04045 ? Math.pow(((b + 0.055) / 1.055), 2.4) : (b / 12.92);

	var x = (r * 0.4124) + (g * 0.3576) + (b * 0.1805);
	var y = (r * 0.2126) + (g * 0.7152) + (b * 0.0722);
	var z = (r * 0.0193) + (g * 0.1192) + (b * 0.9505);

	return [x * 100, y * 100, z * 100];
};

convert.rgb.lab = function (rgb) {
	var xyz = convert.rgb.xyz(rgb);
	var x = xyz[0];
	var y = xyz[1];
	var z = xyz[2];
	var l;
	var a;
	var b;

	x /= 95.047;
	y /= 100;
	z /= 108.883;

	x = x > 0.008856 ? Math.pow(x, 1 / 3) : (7.787 * x) + (16 / 116);
	y = y > 0.008856 ? Math.pow(y, 1 / 3) : (7.787 * y) + (16 / 116);
	z = z > 0.008856 ? Math.pow(z, 1 / 3) : (7.787 * z) + (16 / 116);

	l = (116 * y) - 16;
	a = 500 * (x - y);
	b = 200 * (y - z);

	return [l, a, b];
};

convert.hsl.rgb = function (hsl) {
	var h = hsl[0] / 360;
	var s = hsl[1] / 100;
	var l = hsl[2] / 100;
	var t1;
	var t2;
	var t3;
	var rgb;
	var val;

	if (s === 0) {
		val = l * 255;
		return [val, val, val];
	}

	if (l < 0.5) {
		t2 = l * (1 + s);
	} else {
		t2 = l + s - l * s;
	}

	t1 = 2 * l - t2;

	rgb = [0, 0, 0];
	for (var i = 0; i < 3; i++) {
		t3 = h + 1 / 3 * -(i - 1);
		if (t3 < 0) {
			t3++;
		}
		if (t3 > 1) {
			t3--;
		}

		if (6 * t3 < 1) {
			val = t1 + (t2 - t1) * 6 * t3;
		} else if (2 * t3 < 1) {
			val = t2;
		} else if (3 * t3 < 2) {
			val = t1 + (t2 - t1) * (2 / 3 - t3) * 6;
		} else {
			val = t1;
		}

		rgb[i] = val * 255;
	}

	return rgb;
};

convert.hsl.hsv = function (hsl) {
	var h = hsl[0];
	var s = hsl[1] / 100;
	var l = hsl[2] / 100;
	var smin = s;
	var lmin = Math.max(l, 0.01);
	var sv;
	var v;

	l *= 2;
	s *= (l <= 1) ? l : 2 - l;
	smin *= lmin <= 1 ? lmin : 2 - lmin;
	v = (l + s) / 2;
	sv = l === 0 ? (2 * smin) / (lmin + smin) : (2 * s) / (l + s);

	return [h, sv * 100, v * 100];
};

convert.hsv.rgb = function (hsv) {
	var h = hsv[0] / 60;
	var s = hsv[1] / 100;
	var v = hsv[2] / 100;
	var hi = Math.floor(h) % 6;

	var f = h - Math.floor(h);
	var p = 255 * v * (1 - s);
	var q = 255 * v * (1 - (s * f));
	var t = 255 * v * (1 - (s * (1 - f)));
	v *= 255;

	switch (hi) {
		case 0:
			return [v, t, p];
		case 1:
			return [q, v, p];
		case 2:
			return [p, v, t];
		case 3:
			return [p, q, v];
		case 4:
			return [t, p, v];
		case 5:
			return [v, p, q];
	}
};

convert.hsv.hsl = function (hsv) {
	var h = hsv[0];
	var s = hsv[1] / 100;
	var v = hsv[2] / 100;
	var vmin = Math.max(v, 0.01);
	var lmin;
	var sl;
	var l;

	l = (2 - s) * v;
	lmin = (2 - s) * vmin;
	sl = s * vmin;
	sl /= (lmin <= 1) ? lmin : 2 - lmin;
	sl = sl || 0;
	l /= 2;

	return [h, sl * 100, l * 100];
};

// http://dev.w3.org/csswg/css-color/#hwb-to-rgb
convert.hwb.rgb = function (hwb) {
	var h = hwb[0] / 360;
	var wh = hwb[1] / 100;
	var bl = hwb[2] / 100;
	var ratio = wh + bl;
	var i;
	var v;
	var f;
	var n;

	// wh + bl cant be > 1
	if (ratio > 1) {
		wh /= ratio;
		bl /= ratio;
	}

	i = Math.floor(6 * h);
	v = 1 - bl;
	f = 6 * h - i;

	if ((i & 0x01) !== 0) {
		f = 1 - f;
	}

	n = wh + f * (v - wh); // linear interpolation

	var r;
	var g;
	var b;
	switch (i) {
		default:
		case 6:
		case 0: r = v; g = n; b = wh; break;
		case 1: r = n; g = v; b = wh; break;
		case 2: r = wh; g = v; b = n; break;
		case 3: r = wh; g = n; b = v; break;
		case 4: r = n; g = wh; b = v; break;
		case 5: r = v; g = wh; b = n; break;
	}

	return [r * 255, g * 255, b * 255];
};

convert.cmyk.rgb = function (cmyk) {
	var c = cmyk[0] / 100;
	var m = cmyk[1] / 100;
	var y = cmyk[2] / 100;
	var k = cmyk[3] / 100;
	var r;
	var g;
	var b;

	r = 1 - Math.min(1, c * (1 - k) + k);
	g = 1 - Math.min(1, m * (1 - k) + k);
	b = 1 - Math.min(1, y * (1 - k) + k);

	return [r * 255, g * 255, b * 255];
};

convert.xyz.rgb = function (xyz) {
	var x = xyz[0] / 100;
	var y = xyz[1] / 100;
	var z = xyz[2] / 100;
	var r;
	var g;
	var b;

	r = (x * 3.2406) + (y * -1.5372) + (z * -0.4986);
	g = (x * -0.9689) + (y * 1.8758) + (z * 0.0415);
	b = (x * 0.0557) + (y * -0.2040) + (z * 1.0570);

	// assume sRGB
	r = r > 0.0031308
		? ((1.055 * Math.pow(r, 1.0 / 2.4)) - 0.055)
		: r * 12.92;

	g = g > 0.0031308
		? ((1.055 * Math.pow(g, 1.0 / 2.4)) - 0.055)
		: g * 12.92;

	b = b > 0.0031308
		? ((1.055 * Math.pow(b, 1.0 / 2.4)) - 0.055)
		: b * 12.92;

	r = Math.min(Math.max(0, r), 1);
	g = Math.min(Math.max(0, g), 1);
	b = Math.min(Math.max(0, b), 1);

	return [r * 255, g * 255, b * 255];
};

convert.xyz.lab = function (xyz) {
	var x = xyz[0];
	var y = xyz[1];
	var z = xyz[2];
	var l;
	var a;
	var b;

	x /= 95.047;
	y /= 100;
	z /= 108.883;

	x = x > 0.008856 ? Math.pow(x, 1 / 3) : (7.787 * x) + (16 / 116);
	y = y > 0.008856 ? Math.pow(y, 1 / 3) : (7.787 * y) + (16 / 116);
	z = z > 0.008856 ? Math.pow(z, 1 / 3) : (7.787 * z) + (16 / 116);

	l = (116 * y) - 16;
	a = 500 * (x - y);
	b = 200 * (y - z);

	return [l, a, b];
};

convert.lab.xyz = function (lab) {
	var l = lab[0];
	var a = lab[1];
	var b = lab[2];
	var x;
	var y;
	var z;

	y = (l + 16) / 116;
	x = a / 500 + y;
	z = y - b / 200;

	var y2 = Math.pow(y, 3);
	var x2 = Math.pow(x, 3);
	var z2 = Math.pow(z, 3);
	y = y2 > 0.008856 ? y2 : (y - 16 / 116) / 7.787;
	x = x2 > 0.008856 ? x2 : (x - 16 / 116) / 7.787;
	z = z2 > 0.008856 ? z2 : (z - 16 / 116) / 7.787;

	x *= 95.047;
	y *= 100;
	z *= 108.883;

	return [x, y, z];
};

convert.lab.lch = function (lab) {
	var l = lab[0];
	var a = lab[1];
	var b = lab[2];
	var hr;
	var h;
	var c;

	hr = Math.atan2(b, a);
	h = hr * 360 / 2 / Math.PI;

	if (h < 0) {
		h += 360;
	}

	c = Math.sqrt(a * a + b * b);

	return [l, c, h];
};

convert.lch.lab = function (lch) {
	var l = lch[0];
	var c = lch[1];
	var h = lch[2];
	var a;
	var b;
	var hr;

	hr = h / 360 * 2 * Math.PI;
	a = c * Math.cos(hr);
	b = c * Math.sin(hr);

	return [l, a, b];
};

convert.rgb.ansi16 = function (args) {
	var r = args[0];
	var g = args[1];
	var b = args[2];
	var value = 1 in arguments ? arguments[1] : convert.rgb.hsv(args)[2]; // hsv -> ansi16 optimization

	value = Math.round(value / 50);

	if (value === 0) {
		return 30;
	}

	var ansi = 30
		+ ((Math.round(b / 255) << 2)
		| (Math.round(g / 255) << 1)
		| Math.round(r / 255));

	if (value === 2) {
		ansi += 60;
	}

	return ansi;
};

convert.hsv.ansi16 = function (args) {
	// optimization here; we already know the value and don't need to get
	// it converted for us.
	return convert.rgb.ansi16(convert.hsv.rgb(args), args[2]);
};

convert.rgb.ansi256 = function (args) {
	var r = args[0];
	var g = args[1];
	var b = args[2];

	// we use the extended greyscale palette here, with the exception of
	// black and white. normal palette only has 4 greyscale shades.
	if (r === g && g === b) {
		if (r < 8) {
			return 16;
		}

		if (r > 248) {
			return 231;
		}

		return Math.round(((r - 8) / 247) * 24) + 232;
	}

	var ansi = 16
		+ (36 * Math.round(r / 255 * 5))
		+ (6 * Math.round(g / 255 * 5))
		+ Math.round(b / 255 * 5);

	return ansi;
};

convert.ansi16.rgb = function (args) {
	var color = args % 10;

	// handle greyscale
	if (color === 0 || color === 7) {
		if (args > 50) {
			color += 3.5;
		}

		color = color / 10.5 * 255;

		return [color, color, color];
	}

	var mult = (~~(args > 50) + 1) * 0.5;
	var r = ((color & 1) * mult) * 255;
	var g = (((color >> 1) & 1) * mult) * 255;
	var b = (((color >> 2) & 1) * mult) * 255;

	return [r, g, b];
};

convert.ansi256.rgb = function (args) {
	// handle greyscale
	if (args >= 232) {
		var c = (args - 232) * 10 + 8;
		return [c, c, c];
	}

	args -= 16;

	var rem;
	var r = Math.floor(args / 36) / 5 * 255;
	var g = Math.floor((rem = args % 36) / 6) / 5 * 255;
	var b = (rem % 6) / 5 * 255;

	return [r, g, b];
};

convert.rgb.hex = function (args) {
	var integer = ((Math.round(args[0]) & 0xFF) << 16)
		+ ((Math.round(args[1]) & 0xFF) << 8)
		+ (Math.round(args[2]) & 0xFF);

	var string = integer.toString(16).toUpperCase();
	return '000000'.substring(string.length) + string;
};

convert.hex.rgb = function (args) {
	var match = args.toString(16).match(/[a-f0-9]{6}|[a-f0-9]{3}/i);
	if (!match) {
		return [0, 0, 0];
	}

	var colorString = match[0];

	if (match[0].length === 3) {
		colorString = colorString.split('').map(function (char) {
			return char + char;
		}).join('');
	}

	var integer = parseInt(colorString, 16);
	var r = (integer >> 16) & 0xFF;
	var g = (integer >> 8) & 0xFF;
	var b = integer & 0xFF;

	return [r, g, b];
};

convert.rgb.hcg = function (rgb) {
	var r = rgb[0] / 255;
	var g = rgb[1] / 255;
	var b = rgb[2] / 255;
	var max = Math.max(Math.max(r, g), b);
	var min = Math.min(Math.min(r, g), b);
	var chroma = (max - min);
	var grayscale;
	var hue;

	if (chroma < 1) {
		grayscale = min / (1 - chroma);
	} else {
		grayscale = 0;
	}

	if (chroma <= 0) {
		hue = 0;
	} else
	if (max === r) {
		hue = ((g - b) / chroma) % 6;
	} else
	if (max === g) {
		hue = 2 + (b - r) / chroma;
	} else {
		hue = 4 + (r - g) / chroma + 4;
	}

	hue /= 6;
	hue %= 1;

	return [hue * 360, chroma * 100, grayscale * 100];
};

convert.hsl.hcg = function (hsl) {
	var s = hsl[1] / 100;
	var l = hsl[2] / 100;
	var c = 1;
	var f = 0;

	if (l < 0.5) {
		c = 2.0 * s * l;
	} else {
		c = 2.0 * s * (1.0 - l);
	}

	if (c < 1.0) {
		f = (l - 0.5 * c) / (1.0 - c);
	}

	return [hsl[0], c * 100, f * 100];
};

convert.hsv.hcg = function (hsv) {
	var s = hsv[1] / 100;
	var v = hsv[2] / 100;

	var c = s * v;
	var f = 0;

	if (c < 1.0) {
		f = (v - c) / (1 - c);
	}

	return [hsv[0], c * 100, f * 100];
};

convert.hcg.rgb = function (hcg) {
	var h = hcg[0] / 360;
	var c = hcg[1] / 100;
	var g = hcg[2] / 100;

	if (c === 0.0) {
		return [g * 255, g * 255, g * 255];
	}

	var pure = [0, 0, 0];
	var hi = (h % 1) * 6;
	var v = hi % 1;
	var w = 1 - v;
	var mg = 0;

	switch (Math.floor(hi)) {
		case 0:
			pure[0] = 1; pure[1] = v; pure[2] = 0; break;
		case 1:
			pure[0] = w; pure[1] = 1; pure[2] = 0; break;
		case 2:
			pure[0] = 0; pure[1] = 1; pure[2] = v; break;
		case 3:
			pure[0] = 0; pure[1] = w; pure[2] = 1; break;
		case 4:
			pure[0] = v; pure[1] = 0; pure[2] = 1; break;
		default:
			pure[0] = 1; pure[1] = 0; pure[2] = w;
	}

	mg = (1.0 - c) * g;

	return [
		(c * pure[0] + mg) * 255,
		(c * pure[1] + mg) * 255,
		(c * pure[2] + mg) * 255
	];
};

convert.hcg.hsv = function (hcg) {
	var c = hcg[1] / 100;
	var g = hcg[2] / 100;

	var v = c + g * (1.0 - c);
	var f = 0;

	if (v > 0.0) {
		f = c / v;
	}

	return [hcg[0], f * 100, v * 100];
};

convert.hcg.hsl = function (hcg) {
	var c = hcg[1] / 100;
	var g = hcg[2] / 100;

	var l = g * (1.0 - c) + 0.5 * c;
	var s = 0;

	if (l > 0.0 && l < 0.5) {
		s = c / (2 * l);
	} else
	if (l >= 0.5 && l < 1.0) {
		s = c / (2 * (1 - l));
	}

	return [hcg[0], s * 100, l * 100];
};

convert.hcg.hwb = function (hcg) {
	var c = hcg[1] / 100;
	var g = hcg[2] / 100;
	var v = c + g * (1.0 - c);
	return [hcg[0], (v - c) * 100, (1 - v) * 100];
};

convert.hwb.hcg = function (hwb) {
	var w = hwb[1] / 100;
	var b = hwb[2] / 100;
	var v = 1 - b;
	var c = v - w;
	var g = 0;

	if (c < 1) {
		g = (v - c) / (1 - c);
	}

	return [hwb[0], c * 100, g * 100];
};

convert.apple.rgb = function (apple) {
	return [(apple[0] / 65535) * 255, (apple[1] / 65535) * 255, (apple[2] / 65535) * 255];
};

convert.rgb.apple = function (rgb) {
	return [(rgb[0] / 255) * 65535, (rgb[1] / 255) * 65535, (rgb[2] / 255) * 65535];
};

convert.gray.rgb = function (args) {
	return [args[0] / 100 * 255, args[0] / 100 * 255, args[0] / 100 * 255];
};

convert.gray.hsl = convert.gray.hsv = function (args) {
	return [0, 0, args[0]];
};

convert.gray.hwb = function (gray) {
	return [0, 100, gray[0]];
};

convert.gray.cmyk = function (gray) {
	return [0, 0, 0, gray[0]];
};

convert.gray.lab = function (gray) {
	return [gray[0], 0, 0];
};

convert.gray.hex = function (gray) {
	var val = Math.round(gray[0] / 100 * 255) & 0xFF;
	var integer = (val << 16) + (val << 8) + val;

	var string = integer.toString(16).toUpperCase();
	return '000000'.substring(string.length) + string;
};

convert.rgb.gray = function (rgb) {
	var val = (rgb[0] + rgb[1] + rgb[2]) / 3;
	return [val / 255 * 100];
};
});
var conversions_1 = conversions.rgb;
var conversions_2 = conversions.hsl;
var conversions_3 = conversions.hsv;
var conversions_4 = conversions.hwb;
var conversions_5 = conversions.cmyk;
var conversions_6 = conversions.xyz;
var conversions_7 = conversions.lab;
var conversions_8 = conversions.lch;
var conversions_9 = conversions.hex;
var conversions_10 = conversions.keyword;
var conversions_11 = conversions.ansi16;
var conversions_12 = conversions.ansi256;
var conversions_13 = conversions.hcg;
var conversions_14 = conversions.apple;
var conversions_15 = conversions.gray;

/*
	this function routes a model to all other models.

	all functions that are routed have a property `.conversion` attached
	to the returned synthetic function. This property is an array
	of strings, each with the steps in between the 'from' and 'to'
	color models (inclusive).

	conversions that are not possible simply are not included.
*/

function buildGraph() {
	var graph = {};
	// https://jsperf.com/object-keys-vs-for-in-with-closure/3
	var models = Object.keys(conversions);

	for (var len = models.length, i = 0; i < len; i++) {
		graph[models[i]] = {
			// http://jsperf.com/1-vs-infinity
			// micro-opt, but this is simple.
			distance: -1,
			parent: null
		};
	}

	return graph;
}

// https://en.wikipedia.org/wiki/Breadth-first_search
function deriveBFS(fromModel) {
	var graph = buildGraph();
	var queue = [fromModel]; // unshift -> queue -> pop

	graph[fromModel].distance = 0;

	while (queue.length) {
		var current = queue.pop();
		var adjacents = Object.keys(conversions[current]);

		for (var len = adjacents.length, i = 0; i < len; i++) {
			var adjacent = adjacents[i];
			var node = graph[adjacent];

			if (node.distance === -1) {
				node.distance = graph[current].distance + 1;
				node.parent = current;
				queue.unshift(adjacent);
			}
		}
	}

	return graph;
}

function link(from, to) {
	return function (args) {
		return to(from(args));
	};
}

function wrapConversion(toModel, graph) {
	var path = [graph[toModel].parent, toModel];
	var fn = conversions[graph[toModel].parent][toModel];

	var cur = graph[toModel].parent;
	while (graph[cur].parent) {
		path.unshift(graph[cur].parent);
		fn = link(conversions[graph[cur].parent][cur], fn);
		cur = graph[cur].parent;
	}

	fn.conversion = path;
	return fn;
}

var route = function (fromModel) {
	var graph = deriveBFS(fromModel);
	var conversion = {};

	var models = Object.keys(graph);
	for (var len = models.length, i = 0; i < len; i++) {
		var toModel = models[i];
		var node = graph[toModel];

		if (node.parent === null) {
			// no possible conversion, or this node is the source model.
			continue;
		}

		conversion[toModel] = wrapConversion(toModel, graph);
	}

	return conversion;
};

var convert = {};

var models = Object.keys(conversions);

function wrapRaw(fn) {
	var wrappedFn = function (args) {
		if (args === undefined || args === null) {
			return args;
		}

		if (arguments.length > 1) {
			args = Array.prototype.slice.call(arguments);
		}

		return fn(args);
	};

	// preserve .conversion property if there is one
	if ('conversion' in fn) {
		wrappedFn.conversion = fn.conversion;
	}

	return wrappedFn;
}

function wrapRounded(fn) {
	var wrappedFn = function (args) {
		if (args === undefined || args === null) {
			return args;
		}

		if (arguments.length > 1) {
			args = Array.prototype.slice.call(arguments);
		}

		var result = fn(args);

		// we're assuming the result is an array here.
		// see notice in conversions.js; don't use box types
		// in conversion functions.
		if (typeof result === 'object') {
			for (var len = result.length, i = 0; i < len; i++) {
				result[i] = Math.round(result[i]);
			}
		}

		return result;
	};

	// preserve .conversion property if there is one
	if ('conversion' in fn) {
		wrappedFn.conversion = fn.conversion;
	}

	return wrappedFn;
}

models.forEach(function (fromModel) {
	convert[fromModel] = {};

	Object.defineProperty(convert[fromModel], 'channels', {value: conversions[fromModel].channels});
	Object.defineProperty(convert[fromModel], 'labels', {value: conversions[fromModel].labels});

	var routes = route(fromModel);
	var routeModels = Object.keys(routes);

	routeModels.forEach(function (toModel) {
		var fn = routes[toModel];

		convert[fromModel][toModel] = wrapRounded(fn);
		convert[fromModel][toModel].raw = wrapRaw(fn);
	});
});

var colorConvert = convert;

var colorName$1 = {
	"aliceblue": [240, 248, 255],
	"antiquewhite": [250, 235, 215],
	"aqua": [0, 255, 255],
	"aquamarine": [127, 255, 212],
	"azure": [240, 255, 255],
	"beige": [245, 245, 220],
	"bisque": [255, 228, 196],
	"black": [0, 0, 0],
	"blanchedalmond": [255, 235, 205],
	"blue": [0, 0, 255],
	"blueviolet": [138, 43, 226],
	"brown": [165, 42, 42],
	"burlywood": [222, 184, 135],
	"cadetblue": [95, 158, 160],
	"chartreuse": [127, 255, 0],
	"chocolate": [210, 105, 30],
	"coral": [255, 127, 80],
	"cornflowerblue": [100, 149, 237],
	"cornsilk": [255, 248, 220],
	"crimson": [220, 20, 60],
	"cyan": [0, 255, 255],
	"darkblue": [0, 0, 139],
	"darkcyan": [0, 139, 139],
	"darkgoldenrod": [184, 134, 11],
	"darkgray": [169, 169, 169],
	"darkgreen": [0, 100, 0],
	"darkgrey": [169, 169, 169],
	"darkkhaki": [189, 183, 107],
	"darkmagenta": [139, 0, 139],
	"darkolivegreen": [85, 107, 47],
	"darkorange": [255, 140, 0],
	"darkorchid": [153, 50, 204],
	"darkred": [139, 0, 0],
	"darksalmon": [233, 150, 122],
	"darkseagreen": [143, 188, 143],
	"darkslateblue": [72, 61, 139],
	"darkslategray": [47, 79, 79],
	"darkslategrey": [47, 79, 79],
	"darkturquoise": [0, 206, 209],
	"darkviolet": [148, 0, 211],
	"deeppink": [255, 20, 147],
	"deepskyblue": [0, 191, 255],
	"dimgray": [105, 105, 105],
	"dimgrey": [105, 105, 105],
	"dodgerblue": [30, 144, 255],
	"firebrick": [178, 34, 34],
	"floralwhite": [255, 250, 240],
	"forestgreen": [34, 139, 34],
	"fuchsia": [255, 0, 255],
	"gainsboro": [220, 220, 220],
	"ghostwhite": [248, 248, 255],
	"gold": [255, 215, 0],
	"goldenrod": [218, 165, 32],
	"gray": [128, 128, 128],
	"green": [0, 128, 0],
	"greenyellow": [173, 255, 47],
	"grey": [128, 128, 128],
	"honeydew": [240, 255, 240],
	"hotpink": [255, 105, 180],
	"indianred": [205, 92, 92],
	"indigo": [75, 0, 130],
	"ivory": [255, 255, 240],
	"khaki": [240, 230, 140],
	"lavender": [230, 230, 250],
	"lavenderblush": [255, 240, 245],
	"lawngreen": [124, 252, 0],
	"lemonchiffon": [255, 250, 205],
	"lightblue": [173, 216, 230],
	"lightcoral": [240, 128, 128],
	"lightcyan": [224, 255, 255],
	"lightgoldenrodyellow": [250, 250, 210],
	"lightgray": [211, 211, 211],
	"lightgreen": [144, 238, 144],
	"lightgrey": [211, 211, 211],
	"lightpink": [255, 182, 193],
	"lightsalmon": [255, 160, 122],
	"lightseagreen": [32, 178, 170],
	"lightskyblue": [135, 206, 250],
	"lightslategray": [119, 136, 153],
	"lightslategrey": [119, 136, 153],
	"lightsteelblue": [176, 196, 222],
	"lightyellow": [255, 255, 224],
	"lime": [0, 255, 0],
	"limegreen": [50, 205, 50],
	"linen": [250, 240, 230],
	"magenta": [255, 0, 255],
	"maroon": [128, 0, 0],
	"mediumaquamarine": [102, 205, 170],
	"mediumblue": [0, 0, 205],
	"mediumorchid": [186, 85, 211],
	"mediumpurple": [147, 112, 219],
	"mediumseagreen": [60, 179, 113],
	"mediumslateblue": [123, 104, 238],
	"mediumspringgreen": [0, 250, 154],
	"mediumturquoise": [72, 209, 204],
	"mediumvioletred": [199, 21, 133],
	"midnightblue": [25, 25, 112],
	"mintcream": [245, 255, 250],
	"mistyrose": [255, 228, 225],
	"moccasin": [255, 228, 181],
	"navajowhite": [255, 222, 173],
	"navy": [0, 0, 128],
	"oldlace": [253, 245, 230],
	"olive": [128, 128, 0],
	"olivedrab": [107, 142, 35],
	"orange": [255, 165, 0],
	"orangered": [255, 69, 0],
	"orchid": [218, 112, 214],
	"palegoldenrod": [238, 232, 170],
	"palegreen": [152, 251, 152],
	"paleturquoise": [175, 238, 238],
	"palevioletred": [219, 112, 147],
	"papayawhip": [255, 239, 213],
	"peachpuff": [255, 218, 185],
	"peru": [205, 133, 63],
	"pink": [255, 192, 203],
	"plum": [221, 160, 221],
	"powderblue": [176, 224, 230],
	"purple": [128, 0, 128],
	"rebeccapurple": [102, 51, 153],
	"red": [255, 0, 0],
	"rosybrown": [188, 143, 143],
	"royalblue": [65, 105, 225],
	"saddlebrown": [139, 69, 19],
	"salmon": [250, 128, 114],
	"sandybrown": [244, 164, 96],
	"seagreen": [46, 139, 87],
	"seashell": [255, 245, 238],
	"sienna": [160, 82, 45],
	"silver": [192, 192, 192],
	"skyblue": [135, 206, 235],
	"slateblue": [106, 90, 205],
	"slategray": [112, 128, 144],
	"slategrey": [112, 128, 144],
	"snow": [255, 250, 250],
	"springgreen": [0, 255, 127],
	"steelblue": [70, 130, 180],
	"tan": [210, 180, 140],
	"teal": [0, 128, 128],
	"thistle": [216, 191, 216],
	"tomato": [255, 99, 71],
	"turquoise": [64, 224, 208],
	"violet": [238, 130, 238],
	"wheat": [245, 222, 179],
	"white": [255, 255, 255],
	"whitesmoke": [245, 245, 245],
	"yellow": [255, 255, 0],
	"yellowgreen": [154, 205, 50]
};

/* MIT license */


var colorString = {
   getRgba: getRgba,
   getHsla: getHsla,
   getRgb: getRgb,
   getHsl: getHsl,
   getHwb: getHwb,
   getAlpha: getAlpha,

   hexString: hexString,
   rgbString: rgbString,
   rgbaString: rgbaString,
   percentString: percentString,
   percentaString: percentaString,
   hslString: hslString,
   hslaString: hslaString,
   hwbString: hwbString,
   keyword: keyword
};

function getRgba(string) {
   if (!string) {
      return;
   }
   var abbr =  /^#([a-fA-F0-9]{3,4})$/i,
       hex =  /^#([a-fA-F0-9]{6}([a-fA-F0-9]{2})?)$/i,
       rgba = /^rgba?\(\s*([+-]?\d+)\s*,\s*([+-]?\d+)\s*,\s*([+-]?\d+)\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)$/i,
       per = /^rgba?\(\s*([+-]?[\d\.]+)\%\s*,\s*([+-]?[\d\.]+)\%\s*,\s*([+-]?[\d\.]+)\%\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)$/i,
       keyword = /(\w+)/;

   var rgb = [0, 0, 0],
       a = 1,
       match = string.match(abbr),
       hexAlpha = "";
   if (match) {
      match = match[1];
      hexAlpha = match[3];
      for (var i = 0; i < rgb.length; i++) {
         rgb[i] = parseInt(match[i] + match[i], 16);
      }
      if (hexAlpha) {
         a = Math.round((parseInt(hexAlpha + hexAlpha, 16) / 255) * 100) / 100;
      }
   }
   else if (match = string.match(hex)) {
      hexAlpha = match[2];
      match = match[1];
      for (var i = 0; i < rgb.length; i++) {
         rgb[i] = parseInt(match.slice(i * 2, i * 2 + 2), 16);
      }
      if (hexAlpha) {
         a = Math.round((parseInt(hexAlpha, 16) / 255) * 100) / 100;
      }
   }
   else if (match = string.match(rgba)) {
      for (var i = 0; i < rgb.length; i++) {
         rgb[i] = parseInt(match[i + 1]);
      }
      a = parseFloat(match[4]);
   }
   else if (match = string.match(per)) {
      for (var i = 0; i < rgb.length; i++) {
         rgb[i] = Math.round(parseFloat(match[i + 1]) * 2.55);
      }
      a = parseFloat(match[4]);
   }
   else if (match = string.match(keyword)) {
      if (match[1] == "transparent") {
         return [0, 0, 0, 0];
      }
      rgb = colorName$1[match[1]];
      if (!rgb) {
         return;
      }
   }

   for (var i = 0; i < rgb.length; i++) {
      rgb[i] = scale(rgb[i], 0, 255);
   }
   if (!a && a != 0) {
      a = 1;
   }
   else {
      a = scale(a, 0, 1);
   }
   rgb[3] = a;
   return rgb;
}

function getHsla(string) {
   if (!string) {
      return;
   }
   var hsl = /^hsla?\(\s*([+-]?\d+)(?:deg)?\s*,\s*([+-]?[\d\.]+)%\s*,\s*([+-]?[\d\.]+)%\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)/;
   var match = string.match(hsl);
   if (match) {
      var alpha = parseFloat(match[4]);
      var h = scale(parseInt(match[1]), 0, 360),
          s = scale(parseFloat(match[2]), 0, 100),
          l = scale(parseFloat(match[3]), 0, 100),
          a = scale(isNaN(alpha) ? 1 : alpha, 0, 1);
      return [h, s, l, a];
   }
}

function getHwb(string) {
   if (!string) {
      return;
   }
   var hwb = /^hwb\(\s*([+-]?\d+)(?:deg)?\s*,\s*([+-]?[\d\.]+)%\s*,\s*([+-]?[\d\.]+)%\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)/;
   var match = string.match(hwb);
   if (match) {
    var alpha = parseFloat(match[4]);
      var h = scale(parseInt(match[1]), 0, 360),
          w = scale(parseFloat(match[2]), 0, 100),
          b = scale(parseFloat(match[3]), 0, 100),
          a = scale(isNaN(alpha) ? 1 : alpha, 0, 1);
      return [h, w, b, a];
   }
}

function getRgb(string) {
   var rgba = getRgba(string);
   return rgba && rgba.slice(0, 3);
}

function getHsl(string) {
  var hsla = getHsla(string);
  return hsla && hsla.slice(0, 3);
}

function getAlpha(string) {
   var vals = getRgba(string);
   if (vals) {
      return vals[3];
   }
   else if (vals = getHsla(string)) {
      return vals[3];
   }
   else if (vals = getHwb(string)) {
      return vals[3];
   }
}

// generators
function hexString(rgba, a) {
   var a = (a !== undefined && rgba.length === 3) ? a : rgba[3];
   return "#" + hexDouble(rgba[0]) 
              + hexDouble(rgba[1])
              + hexDouble(rgba[2])
              + (
                 (a >= 0 && a < 1)
                 ? hexDouble(Math.round(a * 255))
                 : ""
              );
}

function rgbString(rgba, alpha) {
   if (alpha < 1 || (rgba[3] && rgba[3] < 1)) {
      return rgbaString(rgba, alpha);
   }
   return "rgb(" + rgba[0] + ", " + rgba[1] + ", " + rgba[2] + ")";
}

function rgbaString(rgba, alpha) {
   if (alpha === undefined) {
      alpha = (rgba[3] !== undefined ? rgba[3] : 1);
   }
   return "rgba(" + rgba[0] + ", " + rgba[1] + ", " + rgba[2]
           + ", " + alpha + ")";
}

function percentString(rgba, alpha) {
   if (alpha < 1 || (rgba[3] && rgba[3] < 1)) {
      return percentaString(rgba, alpha);
   }
   var r = Math.round(rgba[0]/255 * 100),
       g = Math.round(rgba[1]/255 * 100),
       b = Math.round(rgba[2]/255 * 100);

   return "rgb(" + r + "%, " + g + "%, " + b + "%)";
}

function percentaString(rgba, alpha) {
   var r = Math.round(rgba[0]/255 * 100),
       g = Math.round(rgba[1]/255 * 100),
       b = Math.round(rgba[2]/255 * 100);
   return "rgba(" + r + "%, " + g + "%, " + b + "%, " + (alpha || rgba[3] || 1) + ")";
}

function hslString(hsla, alpha) {
   if (alpha < 1 || (hsla[3] && hsla[3] < 1)) {
      return hslaString(hsla, alpha);
   }
   return "hsl(" + hsla[0] + ", " + hsla[1] + "%, " + hsla[2] + "%)";
}

function hslaString(hsla, alpha) {
   if (alpha === undefined) {
      alpha = (hsla[3] !== undefined ? hsla[3] : 1);
   }
   return "hsla(" + hsla[0] + ", " + hsla[1] + "%, " + hsla[2] + "%, "
           + alpha + ")";
}

// hwb is a bit different than rgb(a) & hsl(a) since there is no alpha specific syntax
// (hwb have alpha optional & 1 is default value)
function hwbString(hwb, alpha) {
   if (alpha === undefined) {
      alpha = (hwb[3] !== undefined ? hwb[3] : 1);
   }
   return "hwb(" + hwb[0] + ", " + hwb[1] + "%, " + hwb[2] + "%"
           + (alpha !== undefined && alpha !== 1 ? ", " + alpha : "") + ")";
}

function keyword(rgb) {
  return reverseNames[rgb.slice(0, 3)];
}

// helpers
function scale(num, min, max) {
   return Math.min(Math.max(min, num), max);
}

function hexDouble(num) {
  var str = num.toString(16).toUpperCase();
  return (str.length < 2) ? "0" + str : str;
}


//create a list of reverse color names
var reverseNames = {};
for (var name in colorName$1) {
   reverseNames[colorName$1[name]] = name;
}

/* MIT license */



var Color = function (obj) {
	if (obj instanceof Color) {
		return obj;
	}
	if (!(this instanceof Color)) {
		return new Color(obj);
	}

	this.valid = false;
	this.values = {
		rgb: [0, 0, 0],
		hsl: [0, 0, 0],
		hsv: [0, 0, 0],
		hwb: [0, 0, 0],
		cmyk: [0, 0, 0, 0],
		alpha: 1
	};

	// parse Color() argument
	var vals;
	if (typeof obj === 'string') {
		vals = colorString.getRgba(obj);
		if (vals) {
			this.setValues('rgb', vals);
		} else if (vals = colorString.getHsla(obj)) {
			this.setValues('hsl', vals);
		} else if (vals = colorString.getHwb(obj)) {
			this.setValues('hwb', vals);
		}
	} else if (typeof obj === 'object') {
		vals = obj;
		if (vals.r !== undefined || vals.red !== undefined) {
			this.setValues('rgb', vals);
		} else if (vals.l !== undefined || vals.lightness !== undefined) {
			this.setValues('hsl', vals);
		} else if (vals.v !== undefined || vals.value !== undefined) {
			this.setValues('hsv', vals);
		} else if (vals.w !== undefined || vals.whiteness !== undefined) {
			this.setValues('hwb', vals);
		} else if (vals.c !== undefined || vals.cyan !== undefined) {
			this.setValues('cmyk', vals);
		}
	}
};

Color.prototype = {
	isValid: function () {
		return this.valid;
	},
	rgb: function () {
		return this.setSpace('rgb', arguments);
	},
	hsl: function () {
		return this.setSpace('hsl', arguments);
	},
	hsv: function () {
		return this.setSpace('hsv', arguments);
	},
	hwb: function () {
		return this.setSpace('hwb', arguments);
	},
	cmyk: function () {
		return this.setSpace('cmyk', arguments);
	},

	rgbArray: function () {
		return this.values.rgb;
	},
	hslArray: function () {
		return this.values.hsl;
	},
	hsvArray: function () {
		return this.values.hsv;
	},
	hwbArray: function () {
		var values = this.values;
		if (values.alpha !== 1) {
			return values.hwb.concat([values.alpha]);
		}
		return values.hwb;
	},
	cmykArray: function () {
		return this.values.cmyk;
	},
	rgbaArray: function () {
		var values = this.values;
		return values.rgb.concat([values.alpha]);
	},
	hslaArray: function () {
		var values = this.values;
		return values.hsl.concat([values.alpha]);
	},
	alpha: function (val) {
		if (val === undefined) {
			return this.values.alpha;
		}
		this.setValues('alpha', val);
		return this;
	},

	red: function (val) {
		return this.setChannel('rgb', 0, val);
	},
	green: function (val) {
		return this.setChannel('rgb', 1, val);
	},
	blue: function (val) {
		return this.setChannel('rgb', 2, val);
	},
	hue: function (val) {
		if (val) {
			val %= 360;
			val = val < 0 ? 360 + val : val;
		}
		return this.setChannel('hsl', 0, val);
	},
	saturation: function (val) {
		return this.setChannel('hsl', 1, val);
	},
	lightness: function (val) {
		return this.setChannel('hsl', 2, val);
	},
	saturationv: function (val) {
		return this.setChannel('hsv', 1, val);
	},
	whiteness: function (val) {
		return this.setChannel('hwb', 1, val);
	},
	blackness: function (val) {
		return this.setChannel('hwb', 2, val);
	},
	value: function (val) {
		return this.setChannel('hsv', 2, val);
	},
	cyan: function (val) {
		return this.setChannel('cmyk', 0, val);
	},
	magenta: function (val) {
		return this.setChannel('cmyk', 1, val);
	},
	yellow: function (val) {
		return this.setChannel('cmyk', 2, val);
	},
	black: function (val) {
		return this.setChannel('cmyk', 3, val);
	},

	hexString: function () {
		return colorString.hexString(this.values.rgb);
	},
	rgbString: function () {
		return colorString.rgbString(this.values.rgb, this.values.alpha);
	},
	rgbaString: function () {
		return colorString.rgbaString(this.values.rgb, this.values.alpha);
	},
	percentString: function () {
		return colorString.percentString(this.values.rgb, this.values.alpha);
	},
	hslString: function () {
		return colorString.hslString(this.values.hsl, this.values.alpha);
	},
	hslaString: function () {
		return colorString.hslaString(this.values.hsl, this.values.alpha);
	},
	hwbString: function () {
		return colorString.hwbString(this.values.hwb, this.values.alpha);
	},
	keyword: function () {
		return colorString.keyword(this.values.rgb, this.values.alpha);
	},

	rgbNumber: function () {
		var rgb = this.values.rgb;
		return (rgb[0] << 16) | (rgb[1] << 8) | rgb[2];
	},

	luminosity: function () {
		// http://www.w3.org/TR/WCAG20/#relativeluminancedef
		var rgb = this.values.rgb;
		var lum = [];
		for (var i = 0; i < rgb.length; i++) {
			var chan = rgb[i] / 255;
			lum[i] = (chan <= 0.03928) ? chan / 12.92 : Math.pow(((chan + 0.055) / 1.055), 2.4);
		}
		return 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
	},

	contrast: function (color2) {
		// http://www.w3.org/TR/WCAG20/#contrast-ratiodef
		var lum1 = this.luminosity();
		var lum2 = color2.luminosity();
		if (lum1 > lum2) {
			return (lum1 + 0.05) / (lum2 + 0.05);
		}
		return (lum2 + 0.05) / (lum1 + 0.05);
	},

	level: function (color2) {
		var contrastRatio = this.contrast(color2);
		if (contrastRatio >= 7.1) {
			return 'AAA';
		}

		return (contrastRatio >= 4.5) ? 'AA' : '';
	},

	dark: function () {
		// YIQ equation from http://24ways.org/2010/calculating-color-contrast
		var rgb = this.values.rgb;
		var yiq = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
		return yiq < 128;
	},

	light: function () {
		return !this.dark();
	},

	negate: function () {
		var rgb = [];
		for (var i = 0; i < 3; i++) {
			rgb[i] = 255 - this.values.rgb[i];
		}
		this.setValues('rgb', rgb);
		return this;
	},

	lighten: function (ratio) {
		var hsl = this.values.hsl;
		hsl[2] += hsl[2] * ratio;
		this.setValues('hsl', hsl);
		return this;
	},

	darken: function (ratio) {
		var hsl = this.values.hsl;
		hsl[2] -= hsl[2] * ratio;
		this.setValues('hsl', hsl);
		return this;
	},

	saturate: function (ratio) {
		var hsl = this.values.hsl;
		hsl[1] += hsl[1] * ratio;
		this.setValues('hsl', hsl);
		return this;
	},

	desaturate: function (ratio) {
		var hsl = this.values.hsl;
		hsl[1] -= hsl[1] * ratio;
		this.setValues('hsl', hsl);
		return this;
	},

	whiten: function (ratio) {
		var hwb = this.values.hwb;
		hwb[1] += hwb[1] * ratio;
		this.setValues('hwb', hwb);
		return this;
	},

	blacken: function (ratio) {
		var hwb = this.values.hwb;
		hwb[2] += hwb[2] * ratio;
		this.setValues('hwb', hwb);
		return this;
	},

	greyscale: function () {
		var rgb = this.values.rgb;
		// http://en.wikipedia.org/wiki/Grayscale#Converting_color_to_grayscale
		var val = rgb[0] * 0.3 + rgb[1] * 0.59 + rgb[2] * 0.11;
		this.setValues('rgb', [val, val, val]);
		return this;
	},

	clearer: function (ratio) {
		var alpha = this.values.alpha;
		this.setValues('alpha', alpha - (alpha * ratio));
		return this;
	},

	opaquer: function (ratio) {
		var alpha = this.values.alpha;
		this.setValues('alpha', alpha + (alpha * ratio));
		return this;
	},

	rotate: function (degrees) {
		var hsl = this.values.hsl;
		var hue = (hsl[0] + degrees) % 360;
		hsl[0] = hue < 0 ? 360 + hue : hue;
		this.setValues('hsl', hsl);
		return this;
	},

	/**
	 * Ported from sass implementation in C
	 * https://github.com/sass/libsass/blob/0e6b4a2850092356aa3ece07c6b249f0221caced/functions.cpp#L209
	 */
	mix: function (mixinColor, weight) {
		var color1 = this;
		var color2 = mixinColor;
		var p = weight === undefined ? 0.5 : weight;

		var w = 2 * p - 1;
		var a = color1.alpha() - color2.alpha();

		var w1 = (((w * a === -1) ? w : (w + a) / (1 + w * a)) + 1) / 2.0;
		var w2 = 1 - w1;

		return this
			.rgb(
				w1 * color1.red() + w2 * color2.red(),
				w1 * color1.green() + w2 * color2.green(),
				w1 * color1.blue() + w2 * color2.blue()
			)
			.alpha(color1.alpha() * p + color2.alpha() * (1 - p));
	},

	toJSON: function () {
		return this.rgb();
	},

	clone: function () {
		// NOTE(SB): using node-clone creates a dependency to Buffer when using browserify,
		// making the final build way to big to embed in Chart.js. So let's do it manually,
		// assuming that values to clone are 1 dimension arrays containing only numbers,
		// except 'alpha' which is a number.
		var result = new Color();
		var source = this.values;
		var target = result.values;
		var value, type;

		for (var prop in source) {
			if (source.hasOwnProperty(prop)) {
				value = source[prop];
				type = ({}).toString.call(value);
				if (type === '[object Array]') {
					target[prop] = value.slice(0);
				} else if (type === '[object Number]') {
					target[prop] = value;
				} else {
					console.error('unexpected color value:', value);
				}
			}
		}

		return result;
	}
};

Color.prototype.spaces = {
	rgb: ['red', 'green', 'blue'],
	hsl: ['hue', 'saturation', 'lightness'],
	hsv: ['hue', 'saturation', 'value'],
	hwb: ['hue', 'whiteness', 'blackness'],
	cmyk: ['cyan', 'magenta', 'yellow', 'black']
};

Color.prototype.maxes = {
	rgb: [255, 255, 255],
	hsl: [360, 100, 100],
	hsv: [360, 100, 100],
	hwb: [360, 100, 100],
	cmyk: [100, 100, 100, 100]
};

Color.prototype.getValues = function (space) {
	var values = this.values;
	var vals = {};

	for (var i = 0; i < space.length; i++) {
		vals[space.charAt(i)] = values[space][i];
	}

	if (values.alpha !== 1) {
		vals.a = values.alpha;
	}

	// {r: 255, g: 255, b: 255, a: 0.4}
	return vals;
};

Color.prototype.setValues = function (space, vals) {
	var values = this.values;
	var spaces = this.spaces;
	var maxes = this.maxes;
	var alpha = 1;
	var i;

	this.valid = true;

	if (space === 'alpha') {
		alpha = vals;
	} else if (vals.length) {
		// [10, 10, 10]
		values[space] = vals.slice(0, space.length);
		alpha = vals[space.length];
	} else if (vals[space.charAt(0)] !== undefined) {
		// {r: 10, g: 10, b: 10}
		for (i = 0; i < space.length; i++) {
			values[space][i] = vals[space.charAt(i)];
		}

		alpha = vals.a;
	} else if (vals[spaces[space][0]] !== undefined) {
		// {red: 10, green: 10, blue: 10}
		var chans = spaces[space];

		for (i = 0; i < space.length; i++) {
			values[space][i] = vals[chans[i]];
		}

		alpha = vals.alpha;
	}

	values.alpha = Math.max(0, Math.min(1, (alpha === undefined ? values.alpha : alpha)));

	if (space === 'alpha') {
		return false;
	}

	var capped;

	// cap values of the space prior converting all values
	for (i = 0; i < space.length; i++) {
		capped = Math.max(0, Math.min(maxes[space][i], values[space][i]));
		values[space][i] = Math.round(capped);
	}

	// convert to all the other color spaces
	for (var sname in spaces) {
		if (sname !== space) {
			values[sname] = colorConvert[space][sname](values[space]);
		}
	}

	return true;
};

Color.prototype.setSpace = function (space, args) {
	var vals = args[0];

	if (vals === undefined) {
		// color.rgb()
		return this.getValues(space);
	}

	// color.rgb(10, 10, 10)
	if (typeof vals === 'number') {
		vals = Array.prototype.slice.call(args);
	}

	this.setValues(space, vals);
	return this;
};

Color.prototype.setChannel = function (space, index, val) {
	var svalues = this.values[space];
	if (val === undefined) {
		// color.red()
		return svalues[index];
	} else if (val === svalues[index]) {
		// color.red(color.red())
		return this;
	}

	// color.red(100)
	svalues[index] = val;
	this.setValues(space, svalues);

	return this;
};

if (typeof window !== 'undefined') {
	window.Color = Color;
}

var chartjsColor = Color;

function isValidKey(key) {
	return ['__proto__', 'prototype', 'constructor'].indexOf(key) === -1;
}

/**
 * @namespace Chart.helpers
 */
var helpers = {
	/**
	 * An empty function that can be used, for example, for optional callback.
	 */
	noop: function() {},

	/**
	 * Returns a unique id, sequentially generated from a global variable.
	 * @returns {number}
	 * @function
	 */
	uid: (function() {
		var id = 0;
		return function() {
			return id++;
		};
	}()),

	/**
	 * Returns true if `value` is neither null nor undefined, else returns false.
	 * @param {*} value - The value to test.
	 * @returns {boolean}
	 * @since 2.7.0
	 */
	isNullOrUndef: function(value) {
		return value === null || typeof value === 'undefined';
	},

	/**
	 * Returns true if `value` is an array (including typed arrays), else returns false.
	 * @param {*} value - The value to test.
	 * @returns {boolean}
	 * @function
	 */
	isArray: function(value) {
		if (Array.isArray && Array.isArray(value)) {
			return true;
		}
		var type = Object.prototype.toString.call(value);
		if (type.substr(0, 7) === '[object' && type.substr(-6) === 'Array]') {
			return true;
		}
		return false;
	},

	/**
	 * Returns true if `value` is an object (excluding null), else returns false.
	 * @param {*} value - The value to test.
	 * @returns {boolean}
	 * @since 2.7.0
	 */
	isObject: function(value) {
		return value !== null && Object.prototype.toString.call(value) === '[object Object]';
	},

	/**
	 * Returns true if `value` is a finite number, else returns false
	 * @param {*} value  - The value to test.
	 * @returns {boolean}
	 */
	isFinite: function(value) {
		return (typeof value === 'number' || value instanceof Number) && isFinite(value);
	},

	/**
	 * Returns `value` if defined, else returns `defaultValue`.
	 * @param {*} value - The value to return if defined.
	 * @param {*} defaultValue - The value to return if `value` is undefined.
	 * @returns {*}
	 */
	valueOrDefault: function(value, defaultValue) {
		return typeof value === 'undefined' ? defaultValue : value;
	},

	/**
	 * Returns value at the given `index` in array if defined, else returns `defaultValue`.
	 * @param {Array} value - The array to lookup for value at `index`.
	 * @param {number} index - The index in `value` to lookup for value.
	 * @param {*} defaultValue - The value to return if `value[index]` is undefined.
	 * @returns {*}
	 */
	valueAtIndexOrDefault: function(value, index, defaultValue) {
		return helpers.valueOrDefault(helpers.isArray(value) ? value[index] : value, defaultValue);
	},

	/**
	 * Calls `fn` with the given `args` in the scope defined by `thisArg` and returns the
	 * value returned by `fn`. If `fn` is not a function, this method returns undefined.
	 * @param {function} fn - The function to call.
	 * @param {Array|undefined|null} args - The arguments with which `fn` should be called.
	 * @param {object} [thisArg] - The value of `this` provided for the call to `fn`.
	 * @returns {*}
	 */
	callback: function(fn, args, thisArg) {
		if (fn && typeof fn.call === 'function') {
			return fn.apply(thisArg, args);
		}
	},

	/**
	 * Note(SB) for performance sake, this method should only be used when loopable type
	 * is unknown or in none intensive code (not called often and small loopable). Else
	 * it's preferable to use a regular for() loop and save extra function calls.
	 * @param {object|Array} loopable - The object or array to be iterated.
	 * @param {function} fn - The function to call for each item.
	 * @param {object} [thisArg] - The value of `this` provided for the call to `fn`.
	 * @param {boolean} [reverse] - If true, iterates backward on the loopable.
	 */
	each: function(loopable, fn, thisArg, reverse) {
		var i, len, keys;
		if (helpers.isArray(loopable)) {
			len = loopable.length;
			if (reverse) {
				for (i = len - 1; i >= 0; i--) {
					fn.call(thisArg, loopable[i], i);
				}
			} else {
				for (i = 0; i < len; i++) {
					fn.call(thisArg, loopable[i], i);
				}
			}
		} else if (helpers.isObject(loopable)) {
			keys = Object.keys(loopable);
			len = keys.length;
			for (i = 0; i < len; i++) {
				fn.call(thisArg, loopable[keys[i]], keys[i]);
			}
		}
	},

	/**
	 * Returns true if the `a0` and `a1` arrays have the same content, else returns false.
	 * @see https://stackoverflow.com/a/14853974
	 * @param {Array} a0 - The array to compare
	 * @param {Array} a1 - The array to compare
	 * @returns {boolean}
	 */
	arrayEquals: function(a0, a1) {
		var i, ilen, v0, v1;

		if (!a0 || !a1 || a0.length !== a1.length) {
			return false;
		}

		for (i = 0, ilen = a0.length; i < ilen; ++i) {
			v0 = a0[i];
			v1 = a1[i];

			if (v0 instanceof Array && v1 instanceof Array) {
				if (!helpers.arrayEquals(v0, v1)) {
					return false;
				}
			} else if (v0 !== v1) {
				// NOTE: two different object instances will never be equal: {x:20} != {x:20}
				return false;
			}
		}

		return true;
	},

	/**
	 * Returns a deep copy of `source` without keeping references on objects and arrays.
	 * @param {*} source - The value to clone.
	 * @returns {*}
	 */
	clone: function(source) {
		if (helpers.isArray(source)) {
			return source.map(helpers.clone);
		}

		if (helpers.isObject(source)) {
			var target = Object.create(source);
			var keys = Object.keys(source);
			var klen = keys.length;
			var k = 0;

			for (; k < klen; ++k) {
				target[keys[k]] = helpers.clone(source[keys[k]]);
			}

			return target;
		}

		return source;
	},

	/**
	 * The default merger when Chart.helpers.merge is called without merger option.
	 * Note(SB): also used by mergeConfig and mergeScaleConfig as fallback.
	 * @private
	 */
	_merger: function(key, target, source, options) {
		if (!isValidKey(key)) {
			// We want to ensure we do not copy prototypes over
			// as this can pollute global namespaces
			return;
		}

		var tval = target[key];
		var sval = source[key];

		if (helpers.isObject(tval) && helpers.isObject(sval)) {
			helpers.merge(tval, sval, options);
		} else {
			target[key] = helpers.clone(sval);
		}
	},

	/**
	 * Merges source[key] in target[key] only if target[key] is undefined.
	 * @private
	 */
	_mergerIf: function(key, target, source) {
		if (!isValidKey(key)) {
			// We want to ensure we do not copy prototypes over
			// as this can pollute global namespaces
			return;
		}

		var tval = target[key];
		var sval = source[key];

		if (helpers.isObject(tval) && helpers.isObject(sval)) {
			helpers.mergeIf(tval, sval);
		} else if (!target.hasOwnProperty(key)) {
			target[key] = helpers.clone(sval);
		}
	},

	/**
	 * Recursively deep copies `source` properties into `target` with the given `options`.
	 * IMPORTANT: `target` is not cloned and will be updated with `source` properties.
	 * @param {object} target - The target object in which all sources are merged into.
	 * @param {object|object[]} source - Object(s) to merge into `target`.
	 * @param {object} [options] - Merging options:
	 * @param {function} [options.merger] - The merge method (key, target, source, options)
	 * @returns {object} The `target` object.
	 */
	merge: function(target, source, options) {
		var sources = helpers.isArray(source) ? source : [source];
		var ilen = sources.length;
		var merge, i, keys, klen, k;

		if (!helpers.isObject(target)) {
			return target;
		}

		options = options || {};
		merge = options.merger || helpers._merger;

		for (i = 0; i < ilen; ++i) {
			source = sources[i];
			if (!helpers.isObject(source)) {
				continue;
			}

			keys = Object.keys(source);
			for (k = 0, klen = keys.length; k < klen; ++k) {
				merge(keys[k], target, source, options);
			}
		}

		return target;
	},

	/**
	 * Recursively deep copies `source` properties into `target` *only* if not defined in target.
	 * IMPORTANT: `target` is not cloned and will be updated with `source` properties.
	 * @param {object} target - The target object in which all sources are merged into.
	 * @param {object|object[]} source - Object(s) to merge into `target`.
	 * @returns {object} The `target` object.
	 */
	mergeIf: function(target, source) {
		return helpers.merge(target, source, {merger: helpers._mergerIf});
	},

	/**
	 * Applies the contents of two or more objects together into the first object.
	 * @param {object} target - The target object in which all objects are merged into.
	 * @param {object} arg1 - Object containing additional properties to merge in target.
	 * @param {object} argN - Additional objects containing properties to merge in target.
	 * @returns {object} The `target` object.
	 */
	extend: Object.assign || function(target) {
		return helpers.merge(target, [].slice.call(arguments, 1), {
			merger: function(key, dst, src) {
				dst[key] = src[key];
			}
		});
	},

	/**
	 * Basic javascript inheritance based on the model created in Backbone.js
	 */
	inherits: function(extensions) {
		var me = this;
		var ChartElement = (extensions && extensions.hasOwnProperty('constructor')) ? extensions.constructor : function() {
			return me.apply(this, arguments);
		};

		var Surrogate = function() {
			this.constructor = ChartElement;
		};

		Surrogate.prototype = me.prototype;
		ChartElement.prototype = new Surrogate();
		ChartElement.extend = helpers.inherits;

		if (extensions) {
			helpers.extend(ChartElement.prototype, extensions);
		}

		ChartElement.__super__ = me.prototype;
		return ChartElement;
	},

	_deprecated: function(scope, value, previous, current) {
		if (value !== undefined) {
			console.warn(scope + ': "' + previous +
				'" is deprecated. Please use "' + current + '" instead');
		}
	}
};

var helpers_core = helpers;

// DEPRECATIONS

/**
 * Provided for backward compatibility, use Chart.helpers.callback instead.
 * @function Chart.helpers.callCallback
 * @deprecated since version 2.6.0
 * @todo remove at version 3
 * @private
 */
helpers.callCallback = helpers.callback;

/**
 * Provided for backward compatibility, use Array.prototype.indexOf instead.
 * Array.prototype.indexOf compatibility: Chrome, Opera, Safari, FF1.5+, IE9+
 * @function Chart.helpers.indexOf
 * @deprecated since version 2.7.0
 * @todo remove at version 3
 * @private
 */
helpers.indexOf = function(array, item, fromIndex) {
	return Array.prototype.indexOf.call(array, item, fromIndex);
};

/**
 * Provided for backward compatibility, use Chart.helpers.valueOrDefault instead.
 * @function Chart.helpers.getValueOrDefault
 * @deprecated since version 2.7.0
 * @todo remove at version 3
 * @private
 */
helpers.getValueOrDefault = helpers.valueOrDefault;

/**
 * Provided for backward compatibility, use Chart.helpers.valueAtIndexOrDefault instead.
 * @function Chart.helpers.getValueAtIndexOrDefault
 * @deprecated since version 2.7.0
 * @todo remove at version 3
 * @private
 */
helpers.getValueAtIndexOrDefault = helpers.valueAtIndexOrDefault;

/**
 * Easing functions adapted from Robert Penner's easing equations.
 * @namespace Chart.helpers.easingEffects
 * @see http://www.robertpenner.com/easing/
 */
var effects = {
	linear: function(t) {
		return t;
	},

	easeInQuad: function(t) {
		return t * t;
	},

	easeOutQuad: function(t) {
		return -t * (t - 2);
	},

	easeInOutQuad: function(t) {
		if ((t /= 0.5) < 1) {
			return 0.5 * t * t;
		}
		return -0.5 * ((--t) * (t - 2) - 1);
	},

	easeInCubic: function(t) {
		return t * t * t;
	},

	easeOutCubic: function(t) {
		return (t = t - 1) * t * t + 1;
	},

	easeInOutCubic: function(t) {
		if ((t /= 0.5) < 1) {
			return 0.5 * t * t * t;
		}
		return 0.5 * ((t -= 2) * t * t + 2);
	},

	easeInQuart: function(t) {
		return t * t * t * t;
	},

	easeOutQuart: function(t) {
		return -((t = t - 1) * t * t * t - 1);
	},

	easeInOutQuart: function(t) {
		if ((t /= 0.5) < 1) {
			return 0.5 * t * t * t * t;
		}
		return -0.5 * ((t -= 2) * t * t * t - 2);
	},

	easeInQuint: function(t) {
		return t * t * t * t * t;
	},

	easeOutQuint: function(t) {
		return (t = t - 1) * t * t * t * t + 1;
	},

	easeInOutQuint: function(t) {
		if ((t /= 0.5) < 1) {
			return 0.5 * t * t * t * t * t;
		}
		return 0.5 * ((t -= 2) * t * t * t * t + 2);
	},

	easeInSine: function(t) {
		return -Math.cos(t * (Math.PI / 2)) + 1;
	},

	easeOutSine: function(t) {
		return Math.sin(t * (Math.PI / 2));
	},

	easeInOutSine: function(t) {
		return -0.5 * (Math.cos(Math.PI * t) - 1);
	},

	easeInExpo: function(t) {
		return (t === 0) ? 0 : Math.pow(2, 10 * (t - 1));
	},

	easeOutExpo: function(t) {
		return (t === 1) ? 1 : -Math.pow(2, -10 * t) + 1;
	},

	easeInOutExpo: function(t) {
		if (t === 0) {
			return 0;
		}
		if (t === 1) {
			return 1;
		}
		if ((t /= 0.5) < 1) {
			return 0.5 * Math.pow(2, 10 * (t - 1));
		}
		return 0.5 * (-Math.pow(2, -10 * --t) + 2);
	},

	easeInCirc: function(t) {
		if (t >= 1) {
			return t;
		}
		return -(Math.sqrt(1 - t * t) - 1);
	},

	easeOutCirc: function(t) {
		return Math.sqrt(1 - (t = t - 1) * t);
	},

	easeInOutCirc: function(t) {
		if ((t /= 0.5) < 1) {
			return -0.5 * (Math.sqrt(1 - t * t) - 1);
		}
		return 0.5 * (Math.sqrt(1 - (t -= 2) * t) + 1);
	},

	easeInElastic: function(t) {
		var s = 1.70158;
		var p = 0;
		var a = 1;
		if (t === 0) {
			return 0;
		}
		if (t === 1) {
			return 1;
		}
		if (!p) {
			p = 0.3;
		}
		if (a < 1) {
			a = 1;
			s = p / 4;
		} else {
			s = p / (2 * Math.PI) * Math.asin(1 / a);
		}
		return -(a * Math.pow(2, 10 * (t -= 1)) * Math.sin((t - s) * (2 * Math.PI) / p));
	},

	easeOutElastic: function(t) {
		var s = 1.70158;
		var p = 0;
		var a = 1;
		if (t === 0) {
			return 0;
		}
		if (t === 1) {
			return 1;
		}
		if (!p) {
			p = 0.3;
		}
		if (a < 1) {
			a = 1;
			s = p / 4;
		} else {
			s = p / (2 * Math.PI) * Math.asin(1 / a);
		}
		return a * Math.pow(2, -10 * t) * Math.sin((t - s) * (2 * Math.PI) / p) + 1;
	},

	easeInOutElastic: function(t) {
		var s = 1.70158;
		var p = 0;
		var a = 1;
		if (t === 0) {
			return 0;
		}
		if ((t /= 0.5) === 2) {
			return 1;
		}
		if (!p) {
			p = 0.45;
		}
		if (a < 1) {
			a = 1;
			s = p / 4;
		} else {
			s = p / (2 * Math.PI) * Math.asin(1 / a);
		}
		if (t < 1) {
			return -0.5 * (a * Math.pow(2, 10 * (t -= 1)) * Math.sin((t - s) * (2 * Math.PI) / p));
		}
		return a * Math.pow(2, -10 * (t -= 1)) * Math.sin((t - s) * (2 * Math.PI) / p) * 0.5 + 1;
	},
	easeInBack: function(t) {
		var s = 1.70158;
		return t * t * ((s + 1) * t - s);
	},

	easeOutBack: function(t) {
		var s = 1.70158;
		return (t = t - 1) * t * ((s + 1) * t + s) + 1;
	},

	easeInOutBack: function(t) {
		var s = 1.70158;
		if ((t /= 0.5) < 1) {
			return 0.5 * (t * t * (((s *= (1.525)) + 1) * t - s));
		}
		return 0.5 * ((t -= 2) * t * (((s *= (1.525)) + 1) * t + s) + 2);
	},

	easeInBounce: function(t) {
		return 1 - effects.easeOutBounce(1 - t);
	},

	easeOutBounce: function(t) {
		if (t < (1 / 2.75)) {
			return 7.5625 * t * t;
		}
		if (t < (2 / 2.75)) {
			return 7.5625 * (t -= (1.5 / 2.75)) * t + 0.75;
		}
		if (t < (2.5 / 2.75)) {
			return 7.5625 * (t -= (2.25 / 2.75)) * t + 0.9375;
		}
		return 7.5625 * (t -= (2.625 / 2.75)) * t + 0.984375;
	},

	easeInOutBounce: function(t) {
		if (t < 0.5) {
			return effects.easeInBounce(t * 2) * 0.5;
		}
		return effects.easeOutBounce(t * 2 - 1) * 0.5 + 0.5;
	}
};

var helpers_easing = {
	effects: effects
};

// DEPRECATIONS

/**
 * Provided for backward compatibility, use Chart.helpers.easing.effects instead.
 * @function Chart.helpers.easingEffects
 * @deprecated since version 2.7.0
 * @todo remove at version 3
 * @private
 */
helpers_core.easingEffects = effects;

var PI = Math.PI;
var RAD_PER_DEG = PI / 180;
var DOUBLE_PI = PI * 2;
var HALF_PI = PI / 2;
var QUARTER_PI = PI / 4;
var TWO_THIRDS_PI = PI * 2 / 3;

/**
 * @namespace Chart.helpers.canvas
 */
var exports$1 = {
	/**
	 * Clears the entire canvas associated to the given `chart`.
	 * @param {Chart} chart - The chart for which to clear the canvas.
	 */
	clear: function(chart) {
		chart.ctx.clearRect(0, 0, chart.width, chart.height);
	},

	/**
	 * Creates a "path" for a rectangle with rounded corners at position (x, y) with a
	 * given size (width, height) and the same `radius` for all corners.
	 * @param {CanvasRenderingContext2D} ctx - The canvas 2D Context.
	 * @param {number} x - The x axis of the coordinate for the rectangle starting point.
	 * @param {number} y - The y axis of the coordinate for the rectangle starting point.
	 * @param {number} width - The rectangle's width.
	 * @param {number} height - The rectangle's height.
	 * @param {number} radius - The rounded amount (in pixels) for the four corners.
	 * @todo handle `radius` as top-left, top-right, bottom-right, bottom-left array/object?
	 */
	roundedRect: function(ctx, x, y, width, height, radius) {
		if (radius) {
			var r = Math.min(radius, height / 2, width / 2);
			var left = x + r;
			var top = y + r;
			var right = x + width - r;
			var bottom = y + height - r;

			ctx.moveTo(x, top);
			if (left < right && top < bottom) {
				ctx.arc(left, top, r, -PI, -HALF_PI);
				ctx.arc(right, top, r, -HALF_PI, 0);
				ctx.arc(right, bottom, r, 0, HALF_PI);
				ctx.arc(left, bottom, r, HALF_PI, PI);
			} else if (left < right) {
				ctx.moveTo(left, y);
				ctx.arc(right, top, r, -HALF_PI, HALF_PI);
				ctx.arc(left, top, r, HALF_PI, PI + HALF_PI);
			} else if (top < bottom) {
				ctx.arc(left, top, r, -PI, 0);
				ctx.arc(left, bottom, r, 0, PI);
			} else {
				ctx.arc(left, top, r, -PI, PI);
			}
			ctx.closePath();
			ctx.moveTo(x, y);
		} else {
			ctx.rect(x, y, width, height);
		}
	},

	drawPoint: function(ctx, style, radius, x, y, rotation) {
		var type, xOffset, yOffset, size, cornerRadius;
		var rad = (rotation || 0) * RAD_PER_DEG;

		if (style && typeof style === 'object') {
			type = style.toString();
			if (type === '[object HTMLImageElement]' || type === '[object HTMLCanvasElement]') {
				ctx.save();
				ctx.translate(x, y);
				ctx.rotate(rad);
				ctx.drawImage(style, -style.width / 2, -style.height / 2, style.width, style.height);
				ctx.restore();
				return;
			}
		}

		if (isNaN(radius) || radius <= 0) {
			return;
		}

		ctx.beginPath();

		switch (style) {
		// Default includes circle
		default:
			ctx.arc(x, y, radius, 0, DOUBLE_PI);
			ctx.closePath();
			break;
		case 'triangle':
			ctx.moveTo(x + Math.sin(rad) * radius, y - Math.cos(rad) * radius);
			rad += TWO_THIRDS_PI;
			ctx.lineTo(x + Math.sin(rad) * radius, y - Math.cos(rad) * radius);
			rad += TWO_THIRDS_PI;
			ctx.lineTo(x + Math.sin(rad) * radius, y - Math.cos(rad) * radius);
			ctx.closePath();
			break;
		case 'rectRounded':
			// NOTE: the rounded rect implementation changed to use `arc` instead of
			// `quadraticCurveTo` since it generates better results when rect is
			// almost a circle. 0.516 (instead of 0.5) produces results with visually
			// closer proportion to the previous impl and it is inscribed in the
			// circle with `radius`. For more details, see the following PRs:
			// https://github.com/chartjs/Chart.js/issues/5597
			// https://github.com/chartjs/Chart.js/issues/5858
			cornerRadius = radius * 0.516;
			size = radius - cornerRadius;
			xOffset = Math.cos(rad + QUARTER_PI) * size;
			yOffset = Math.sin(rad + QUARTER_PI) * size;
			ctx.arc(x - xOffset, y - yOffset, cornerRadius, rad - PI, rad - HALF_PI);
			ctx.arc(x + yOffset, y - xOffset, cornerRadius, rad - HALF_PI, rad);
			ctx.arc(x + xOffset, y + yOffset, cornerRadius, rad, rad + HALF_PI);
			ctx.arc(x - yOffset, y + xOffset, cornerRadius, rad + HALF_PI, rad + PI);
			ctx.closePath();
			break;
		case 'rect':
			if (!rotation) {
				size = Math.SQRT1_2 * radius;
				ctx.rect(x - size, y - size, 2 * size, 2 * size);
				break;
			}
			rad += QUARTER_PI;
			/* falls through */
		case 'rectRot':
			xOffset = Math.cos(rad) * radius;
			yOffset = Math.sin(rad) * radius;
			ctx.moveTo(x - xOffset, y - yOffset);
			ctx.lineTo(x + yOffset, y - xOffset);
			ctx.lineTo(x + xOffset, y + yOffset);
			ctx.lineTo(x - yOffset, y + xOffset);
			ctx.closePath();
			break;
		case 'crossRot':
			rad += QUARTER_PI;
			/* falls through */
		case 'cross':
			xOffset = Math.cos(rad) * radius;
			yOffset = Math.sin(rad) * radius;
			ctx.moveTo(x - xOffset, y - yOffset);
			ctx.lineTo(x + xOffset, y + yOffset);
			ctx.moveTo(x + yOffset, y - xOffset);
			ctx.lineTo(x - yOffset, y + xOffset);
			break;
		case 'star':
			xOffset = Math.cos(rad) * radius;
			yOffset = Math.sin(rad) * radius;
			ctx.moveTo(x - xOffset, y - yOffset);
			ctx.lineTo(x + xOffset, y + yOffset);
			ctx.moveTo(x + yOffset, y - xOffset);
			ctx.lineTo(x - yOffset, y + xOffset);
			rad += QUARTER_PI;
			xOffset = Math.cos(rad) * radius;
			yOffset = Math.sin(rad) * radius;
			ctx.moveTo(x - xOffset, y - yOffset);
			ctx.lineTo(x + xOffset, y + yOffset);
			ctx.moveTo(x + yOffset, y - xOffset);
			ctx.lineTo(x - yOffset, y + xOffset);
			break;
		case 'line':
			xOffset = Math.cos(rad) * radius;
			yOffset = Math.sin(rad) * radius;
			ctx.moveTo(x - xOffset, y - yOffset);
			ctx.lineTo(x + xOffset, y + yOffset);
			break;
		case 'dash':
			ctx.moveTo(x, y);
			ctx.lineTo(x + Math.cos(rad) * radius, y + Math.sin(rad) * radius);
			break;
		}

		ctx.fill();
		ctx.stroke();
	},

	/**
	 * Returns true if the point is inside the rectangle
	 * @param {object} point - The point to test
	 * @param {object} area - The rectangle
	 * @returns {boolean}
	 * @private
	 */
	_isPointInArea: function(point, area) {
		var epsilon = 1e-6; // 1e-6 is margin in pixels for accumulated error.

		return point.x > area.left - epsilon && point.x < area.right + epsilon &&
			point.y > area.top - epsilon && point.y < area.bottom + epsilon;
	},

	clipArea: function(ctx, area) {
		ctx.save();
		ctx.beginPath();
		ctx.rect(area.left, area.top, area.right - area.left, area.bottom - area.top);
		ctx.clip();
	},

	unclipArea: function(ctx) {
		ctx.restore();
	},

	lineTo: function(ctx, previous, target, flip) {
		var stepped = target.steppedLine;
		if (stepped) {
			if (stepped === 'middle') {
				var midpoint = (previous.x + target.x) / 2.0;
				ctx.lineTo(midpoint, flip ? target.y : previous.y);
				ctx.lineTo(midpoint, flip ? previous.y : target.y);
			} else if ((stepped === 'after' && !flip) || (stepped !== 'after' && flip)) {
				ctx.lineTo(previous.x, target.y);
			} else {
				ctx.lineTo(target.x, previous.y);
			}
			ctx.lineTo(target.x, target.y);
			return;
		}

		if (!target.tension) {
			ctx.lineTo(target.x, target.y);
			return;
		}

		ctx.bezierCurveTo(
			flip ? previous.controlPointPreviousX : previous.controlPointNextX,
			flip ? previous.controlPointPreviousY : previous.controlPointNextY,
			flip ? target.controlPointNextX : target.controlPointPreviousX,
			flip ? target.controlPointNextY : target.controlPointPreviousY,
			target.x,
			target.y);
	}
};

var helpers_canvas = exports$1;

// DEPRECATIONS

/**
 * Provided for backward compatibility, use Chart.helpers.canvas.clear instead.
 * @namespace Chart.helpers.clear
 * @deprecated since version 2.7.0
 * @todo remove at version 3
 * @private
 */
helpers_core.clear = exports$1.clear;

/**
 * Provided for backward compatibility, use Chart.helpers.canvas.roundedRect instead.
 * @namespace Chart.helpers.drawRoundedRectangle
 * @deprecated since version 2.7.0
 * @todo remove at version 3
 * @private
 */
helpers_core.drawRoundedRectangle = function(ctx) {
	ctx.beginPath();
	exports$1.roundedRect.apply(exports$1, arguments);
};

var defaults = {
	/**
	 * @private
	 */
	_set: function(scope, values) {
		return helpers_core.merge(this[scope] || (this[scope] = {}), values);
	}
};

// TODO(v3): remove 'global' from namespace.  all default are global and
// there's inconsistency around which options are under 'global'
defaults._set('global', {
	defaultColor: 'rgba(0,0,0,0.1)',
	defaultFontColor: '#666',
	defaultFontFamily: "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
	defaultFontSize: 12,
	defaultFontStyle: 'normal',
	defaultLineHeight: 1.2,
	showLines: true
});

var core_defaults = defaults;

var valueOrDefault = helpers_core.valueOrDefault;

/**
 * Converts the given font object into a CSS font string.
 * @param {object} font - A font object.
 * @return {string} The CSS font string. See https://developer.mozilla.org/en-US/docs/Web/CSS/font
 * @private
 */
function toFontString(font) {
	if (!font || helpers_core.isNullOrUndef(font.size) || helpers_core.isNullOrUndef(font.family)) {
		return null;
	}

	return (font.style ? font.style + ' ' : '')
		+ (font.weight ? font.weight + ' ' : '')
		+ font.size + 'px '
		+ font.family;
}

/**
 * @alias Chart.helpers.options
 * @namespace
 */
var helpers_options = {
	/**
	 * Converts the given line height `value` in pixels for a specific font `size`.
	 * @param {number|string} value - The lineHeight to parse (eg. 1.6, '14px', '75%', '1.6em').
	 * @param {number} size - The font size (in pixels) used to resolve relative `value`.
	 * @returns {number} The effective line height in pixels (size * 1.2 if value is invalid).
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/line-height
	 * @since 2.7.0
	 */
	toLineHeight: function(value, size) {
		var matches = ('' + value).match(/^(normal|(\d+(?:\.\d+)?)(px|em|%)?)$/);
		if (!matches || matches[1] === 'normal') {
			return size * 1.2;
		}

		value = +matches[2];

		switch (matches[3]) {
		case 'px':
			return value;
		case '%':
			value /= 100;
			break;
		}

		return size * value;
	},

	/**
	 * Converts the given value into a padding object with pre-computed width/height.
	 * @param {number|object} value - If a number, set the value to all TRBL component,
	 *  else, if and object, use defined properties and sets undefined ones to 0.
	 * @returns {object} The padding values (top, right, bottom, left, width, height)
	 * @since 2.7.0
	 */
	toPadding: function(value) {
		var t, r, b, l;

		if (helpers_core.isObject(value)) {
			t = +value.top || 0;
			r = +value.right || 0;
			b = +value.bottom || 0;
			l = +value.left || 0;
		} else {
			t = r = b = l = +value || 0;
		}

		return {
			top: t,
			right: r,
			bottom: b,
			left: l,
			height: t + b,
			width: l + r
		};
	},

	/**
	 * Parses font options and returns the font object.
	 * @param {object} options - A object that contains font options to be parsed.
	 * @return {object} The font object.
	 * @todo Support font.* options and renamed to toFont().
	 * @private
	 */
	_parseFont: function(options) {
		var globalDefaults = core_defaults.global;
		var size = valueOrDefault(options.fontSize, globalDefaults.defaultFontSize);
		var font = {
			family: valueOrDefault(options.fontFamily, globalDefaults.defaultFontFamily),
			lineHeight: helpers_core.options.toLineHeight(valueOrDefault(options.lineHeight, globalDefaults.defaultLineHeight), size),
			size: size,
			style: valueOrDefault(options.fontStyle, globalDefaults.defaultFontStyle),
			weight: null,
			string: ''
		};

		font.string = toFontString(font);
		return font;
	},

	/**
	 * Evaluates the given `inputs` sequentially and returns the first defined value.
	 * @param {Array} inputs - An array of values, falling back to the last value.
	 * @param {object} [context] - If defined and the current value is a function, the value
	 * is called with `context` as first argument and the result becomes the new input.
	 * @param {number} [index] - If defined and the current value is an array, the value
	 * at `index` become the new input.
	 * @param {object} [info] - object to return information about resolution in
	 * @param {boolean} [info.cacheable] - Will be set to `false` if option is not cacheable.
	 * @since 2.7.0
	 */
	resolve: function(inputs, context, index, info) {
		var cacheable = true;
		var i, ilen, value;

		for (i = 0, ilen = inputs.length; i < ilen; ++i) {
			value = inputs[i];
			if (value === undefined) {
				continue;
			}
			if (context !== undefined && typeof value === 'function') {
				value = value(context);
				cacheable = false;
			}
			if (index !== undefined && helpers_core.isArray(value)) {
				value = value[index];
				cacheable = false;
			}
			if (value !== undefined) {
				if (info && !cacheable) {
					info.cacheable = false;
				}
				return value;
			}
		}
	}
};

/**
 * @alias Chart.helpers.math
 * @namespace
 */
var exports$2 = {
	/**
	 * Returns an array of factors sorted from 1 to sqrt(value)
	 * @private
	 */
	_factorize: function(value) {
		var result = [];
		var sqrt = Math.sqrt(value);
		var i;

		for (i = 1; i < sqrt; i++) {
			if (value % i === 0) {
				result.push(i);
				result.push(value / i);
			}
		}
		if (sqrt === (sqrt | 0)) { // if value is a square number
			result.push(sqrt);
		}

		result.sort(function(a, b) {
			return a - b;
		}).pop();
		return result;
	},

	log10: Math.log10 || function(x) {
		var exponent = Math.log(x) * Math.LOG10E; // Math.LOG10E = 1 / Math.LN10.
		// Check for whole powers of 10,
		// which due to floating point rounding error should be corrected.
		var powerOf10 = Math.round(exponent);
		var isPowerOf10 = x === Math.pow(10, powerOf10);

		return isPowerOf10 ? powerOf10 : exponent;
	}
};

var helpers_math = exports$2;

// DEPRECATIONS

/**
 * Provided for backward compatibility, use Chart.helpers.math.log10 instead.
 * @namespace Chart.helpers.log10
 * @deprecated since version 2.9.0
 * @todo remove at version 3
 * @private
 */
helpers_core.log10 = exports$2.log10;

var getRtlAdapter = function(rectX, width) {
	return {
		x: function(x) {
			return rectX + rectX + width - x;
		},
		setWidth: function(w) {
			width = w;
		},
		textAlign: function(align) {
			if (align === 'center') {
				return align;
			}
			return align === 'right' ? 'left' : 'right';
		},
		xPlus: function(x, value) {
			return x - value;
		},
		leftForLtr: function(x, itemWidth) {
			return x - itemWidth;
		},
	};
};

var getLtrAdapter = function() {
	return {
		x: function(x) {
			return x;
		},
		setWidth: function(w) { // eslint-disable-line no-unused-vars
		},
		textAlign: function(align) {
			return align;
		},
		xPlus: function(x, value) {
			return x + value;
		},
		leftForLtr: function(x, _itemWidth) { // eslint-disable-line no-unused-vars
			return x;
		},
	};
};

var getAdapter = function(rtl, rectX, width) {
	return rtl ? getRtlAdapter(rectX, width) : getLtrAdapter();
};

var overrideTextDirection = function(ctx, direction) {
	var style, original;
	if (direction === 'ltr' || direction === 'rtl') {
		style = ctx.canvas.style;
		original = [
			style.getPropertyValue('direction'),
			style.getPropertyPriority('direction'),
		];

		style.setProperty('direction', direction, 'important');
		ctx.prevTextDirection = original;
	}
};

var restoreTextDirection = function(ctx) {
	var original = ctx.prevTextDirection;
	if (original !== undefined) {
		delete ctx.prevTextDirection;
		ctx.canvas.style.setProperty('direction', original[0], original[1]);
	}
};

var helpers_rtl = {
	getRtlAdapter: getAdapter,
	overrideTextDirection: overrideTextDirection,
	restoreTextDirection: restoreTextDirection,
};

var helpers$1 = helpers_core;
var easing = helpers_easing;
var canvas = helpers_canvas;
var options = helpers_options;
var math = helpers_math;
var rtl = helpers_rtl;
helpers$1.easing = easing;
helpers$1.canvas = canvas;
helpers$1.options = options;
helpers$1.math = math;
helpers$1.rtl = rtl;

function interpolate(start, view, model, ease) {
	var keys = Object.keys(model);
	var i, ilen, key, actual, origin, target, type, c0, c1;

	for (i = 0, ilen = keys.length; i < ilen; ++i) {
		key = keys[i];

		target = model[key];

		// if a value is added to the model after pivot() has been called, the view
		// doesn't contain it, so let's initialize the view to the target value.
		if (!view.hasOwnProperty(key)) {
			view[key] = target;
		}

		actual = view[key];

		if (actual === target || key[0] === '_') {
			continue;
		}

		if (!start.hasOwnProperty(key)) {
			start[key] = actual;
		}

		origin = start[key];

		type = typeof target;

		if (type === typeof origin) {
			if (type === 'string') {
				c0 = chartjsColor(origin);
				if (c0.valid) {
					c1 = chartjsColor(target);
					if (c1.valid) {
						view[key] = c1.mix(c0, ease).rgbString();
						continue;
					}
				}
			} else if (helpers$1.isFinite(origin) && helpers$1.isFinite(target)) {
				view[key] = origin + (target - origin) * ease;
				continue;
			}
		}

		view[key] = target;
	}
}

var Element = function(configuration) {
	helpers$1.extend(this, configuration);
	this.initialize.apply(this, arguments);
};

helpers$1.extend(Element.prototype, {
	_type: undefined,

	initialize: function() {
		this.hidden = false;
	},

	pivot: function() {
		var me = this;
		if (!me._view) {
			me._view = helpers$1.extend({}, me._model);
		}
		me._start = {};
		return me;
	},

	transition: function(ease) {
		var me = this;
		var model = me._model;
		var start = me._start;
		var view = me._view;

		// No animation -> No Transition
		if (!model || ease === 1) {
			me._view = helpers$1.extend({}, model);
			me._start = null;
			return me;
		}

		if (!view) {
			view = me._view = {};
		}

		if (!start) {
			start = me._start = {};
		}

		interpolate(start, view, model, ease);

		return me;
	},

	tooltipPosition: function() {
		return {
			x: this._model.x,
			y: this._model.y
		};
	},

	hasValue: function() {
		return helpers$1.isNumber(this._model.x) && helpers$1.isNumber(this._model.y);
	}
});

Element.extend = helpers$1.inherits;

var core_element = Element;

var exports$3 = core_element.extend({
	chart: null, // the animation associated chart instance
	currentStep: 0, // the current animation step
	numSteps: 60, // default number of steps
	easing: '', // the easing to use for this animation
	render: null, // render function used by the animation service

	onAnimationProgress: null, // user specified callback to fire on each step of the animation
	onAnimationComplete: null, // user specified callback to fire when the animation finishes
});

var core_animation = exports$3;

// DEPRECATIONS

/**
 * Provided for backward compatibility, use Chart.Animation instead
 * @prop Chart.Animation#animationObject
 * @deprecated since version 2.6.0
 * @todo remove at version 3
 */
Object.defineProperty(exports$3.prototype, 'animationObject', {
	get: function() {
		return this;
	}
});

/**
 * Provided for backward compatibility, use Chart.Animation#chart instead
 * @prop Chart.Animation#chartInstance
 * @deprecated since version 2.6.0
 * @todo remove at version 3
 */
Object.defineProperty(exports$3.prototype, 'chartInstance', {
	get: function() {
		return this.chart;
	},
	set: function(value) {
		this.chart = value;
	}
});

core_defaults._set('global', {
	animation: {
		duration: 1000,
		easing: 'easeOutQuart',
		onProgress: helpers$1.noop,
		onComplete: helpers$1.noop
	}
});

var core_animations = {
	animations: [],
	request: null,

	/**
	 * @param {Chart} chart - The chart to animate.
	 * @param {Chart.Animation} animation - The animation that we will animate.
	 * @param {number} duration - The animation duration in ms.
	 * @param {boolean} lazy - if true, the chart is not marked as animating to enable more responsive interactions
	 */
	addAnimation: function(chart, animation, duration, lazy) {
		var animations = this.animations;
		var i, ilen;

		animation.chart = chart;
		animation.startTime = Date.now();
		animation.duration = duration;

		if (!lazy) {
			chart.animating = true;
		}

		for (i = 0, ilen = animations.length; i < ilen; ++i) {
			if (animations[i].chart === chart) {
				animations[i] = animation;
				return;
			}
		}

		animations.push(animation);

		// If there are no animations queued, manually kickstart a digest, for lack of a better word
		if (animations.length === 1) {
			this.requestAnimationFrame();
		}
	},

	cancelAnimation: function(chart) {
		var index = helpers$1.findIndex(this.animations, function(animation) {
			return animation.chart === chart;
		});

		if (index !== -1) {
			this.animations.splice(index, 1);
			chart.animating = false;
		}
	},

	requestAnimationFrame: function() {
		var me = this;
		if (me.request === null) {
			// Skip animation frame requests until the active one is executed.
			// This can happen when processing mouse events, e.g. 'mousemove'
			// and 'mouseout' events will trigger multiple renders.
			me.request = helpers$1.requestAnimFrame.call(window, function() {
				me.request = null;
				me.startDigest();
			});
		}
	},

	/**
	 * @private
	 */
	startDigest: function() {
		var me = this;

		me.advance();

		// Do we have more stuff to animate?
		if (me.animations.length > 0) {
			me.requestAnimationFrame();
		}
	},

	/**
	 * @private
	 */
	advance: function() {
		var animations = this.animations;
		var animation, chart, numSteps, nextStep;
		var i = 0;

		// 1 animation per chart, so we are looping charts here
		while (i < animations.length) {
			animation = animations[i];
			chart = animation.chart;
			numSteps = animation.numSteps;

			// Make sure that currentStep starts at 1
			// https://github.com/chartjs/Chart.js/issues/6104
			nextStep = Math.floor((Date.now() - animation.startTime) / animation.duration * numSteps) + 1;
			animation.currentStep = Math.min(nextStep, numSteps);

			helpers$1.callback(animation.render, [chart, animation], chart);
			helpers$1.callback(animation.onAnimationProgress, [animation], chart);

			if (animation.currentStep >= numSteps) {
				helpers$1.callback(animation.onAnimationComplete, [animation], chart);
				chart.animating = false;
				animations.splice(i, 1);
			} else {
				++i;
			}
		}
	}
};

var resolve = helpers$1.options.resolve;

var arrayEvents = ['push', 'pop', 'shift', 'splice', 'unshift'];

/**
 * Hooks the array methods that add or remove values ('push', pop', 'shift', 'splice',
 * 'unshift') and notify the listener AFTER the array has been altered. Listeners are
 * called on the 'onData*' callbacks (e.g. onDataPush, etc.) with same arguments.
 */
function listenArrayEvents(array, listener) {
	if (array._chartjs) {
		array._chartjs.listeners.push(listener);
		return;
	}

	Object.defineProperty(array, '_chartjs', {
		configurable: true,
		enumerable: false,
		value: {
			listeners: [listener]
		}
	});

	arrayEvents.forEach(function(key) {
		var method = 'onData' + key.charAt(0).toUpperCase() + key.slice(1);
		var base = array[key];

		Object.defineProperty(array, key, {
			configurable: true,
			enumerable: false,
			value: function() {
				var args = Array.prototype.slice.call(arguments);
				var res = base.apply(this, args);

				helpers$1.each(array._chartjs.listeners, function(object) {
					if (typeof object[method] === 'function') {
						object[method].apply(object, args);
					}
				});

				return res;
			}
		});
	});
}

/**
 * Removes the given array event listener and cleanup extra attached properties (such as
 * the _chartjs stub and overridden methods) if array doesn't have any more listeners.
 */
function unlistenArrayEvents(array, listener) {
	var stub = array._chartjs;
	if (!stub) {
		return;
	}

	var listeners = stub.listeners;
	var index = listeners.indexOf(listener);
	if (index !== -1) {
		listeners.splice(index, 1);
	}

	if (listeners.length > 0) {
		return;
	}

	arrayEvents.forEach(function(key) {
		delete array[key];
	});

	delete array._chartjs;
}

// Base class for all dataset controllers (line, bar, etc)
var DatasetController = function(chart, datasetIndex) {
	this.initialize(chart, datasetIndex);
};

helpers$1.extend(DatasetController.prototype, {

	/**
	 * Element type used to generate a meta dataset (e.g. Chart.element.Line).
	 * @type {Chart.core.element}
	 */
	datasetElementType: null,

	/**
	 * Element type used to generate a meta data (e.g. Chart.element.Point).
	 * @type {Chart.core.element}
	 */
	dataElementType: null,

	/**
	 * Dataset element option keys to be resolved in _resolveDatasetElementOptions.
	 * A derived controller may override this to resolve controller-specific options.
	 * The keys defined here are for backward compatibility for legend styles.
	 * @private
	 */
	_datasetElementOptions: [
		'backgroundColor',
		'borderCapStyle',
		'borderColor',
		'borderDash',
		'borderDashOffset',
		'borderJoinStyle',
		'borderWidth'
	],

	/**
	 * Data element option keys to be resolved in _resolveDataElementOptions.
	 * A derived controller may override this to resolve controller-specific options.
	 * The keys defined here are for backward compatibility for legend styles.
	 * @private
	 */
	_dataElementOptions: [
		'backgroundColor',
		'borderColor',
		'borderWidth',
		'pointStyle'
	],

	initialize: function(chart, datasetIndex) {
		var me = this;
		me.chart = chart;
		me.index = datasetIndex;
		me.linkScales();
		me.addElements();
		me._type = me.getMeta().type;
	},

	updateIndex: function(datasetIndex) {
		this.index = datasetIndex;
	},

	linkScales: function() {
		var me = this;
		var meta = me.getMeta();
		var chart = me.chart;
		var scales = chart.scales;
		var dataset = me.getDataset();
		var scalesOpts = chart.options.scales;

		if (meta.xAxisID === null || !(meta.xAxisID in scales) || dataset.xAxisID) {
			meta.xAxisID = dataset.xAxisID || scalesOpts.xAxes[0].id;
		}
		if (meta.yAxisID === null || !(meta.yAxisID in scales) || dataset.yAxisID) {
			meta.yAxisID = dataset.yAxisID || scalesOpts.yAxes[0].id;
		}
	},

	getDataset: function() {
		return this.chart.data.datasets[this.index];
	},

	getMeta: function() {
		return this.chart.getDatasetMeta(this.index);
	},

	getScaleForId: function(scaleID) {
		return this.chart.scales[scaleID];
	},

	/**
	 * @private
	 */
	_getValueScaleId: function() {
		return this.getMeta().yAxisID;
	},

	/**
	 * @private
	 */
	_getIndexScaleId: function() {
		return this.getMeta().xAxisID;
	},

	/**
	 * @private
	 */
	_getValueScale: function() {
		return this.getScaleForId(this._getValueScaleId());
	},

	/**
	 * @private
	 */
	_getIndexScale: function() {
		return this.getScaleForId(this._getIndexScaleId());
	},

	reset: function() {
		this._update(true);
	},

	/**
	 * @private
	 */
	destroy: function() {
		if (this._data) {
			unlistenArrayEvents(this._data, this);
		}
	},

	createMetaDataset: function() {
		var me = this;
		var type = me.datasetElementType;
		return type && new type({
			_chart: me.chart,
			_datasetIndex: me.index
		});
	},

	createMetaData: function(index) {
		var me = this;
		var type = me.dataElementType;
		return type && new type({
			_chart: me.chart,
			_datasetIndex: me.index,
			_index: index
		});
	},

	addElements: function() {
		var me = this;
		var meta = me.getMeta();
		var data = me.getDataset().data || [];
		var metaData = meta.data;
		var i, ilen;

		for (i = 0, ilen = data.length; i < ilen; ++i) {
			metaData[i] = metaData[i] || me.createMetaData(i);
		}

		meta.dataset = meta.dataset || me.createMetaDataset();
	},

	addElementAndReset: function(index) {
		var element = this.createMetaData(index);
		this.getMeta().data.splice(index, 0, element);
		this.updateElement(element, index, true);
	},

	buildOrUpdateElements: function() {
		var me = this;
		var dataset = me.getDataset();
		var data = dataset.data || (dataset.data = []);

		// In order to correctly handle data addition/deletion animation (an thus simulate
		// real-time charts), we need to monitor these data modifications and synchronize
		// the internal meta data accordingly.
		if (me._data !== data) {
			if (me._data) {
				// This case happens when the user replaced the data array instance.
				unlistenArrayEvents(me._data, me);
			}

			if (data && Object.isExtensible(data)) {
				listenArrayEvents(data, me);
			}
			me._data = data;
		}

		// Re-sync meta data in case the user replaced the data array or if we missed
		// any updates and so make sure that we handle number of datapoints changing.
		me.resyncElements();
	},

	/**
	 * Returns the merged user-supplied and default dataset-level options
	 * @private
	 */
	_configure: function() {
		var me = this;
		me._config = helpers$1.merge(Object.create(null), [
			me.chart.options.datasets[me._type],
			me.getDataset(),
		], {
			merger: function(key, target, source) {
				if (key !== '_meta' && key !== 'data') {
					helpers$1._merger(key, target, source);
				}
			}
		});
	},

	_update: function(reset) {
		var me = this;
		me._configure();
		me._cachedDataOpts = null;
		me.update(reset);
	},

	update: helpers$1.noop,

	transition: function(easingValue) {
		var meta = this.getMeta();
		var elements = meta.data || [];
		var ilen = elements.length;
		var i = 0;

		for (; i < ilen; ++i) {
			elements[i].transition(easingValue);
		}

		if (meta.dataset) {
			meta.dataset.transition(easingValue);
		}
	},

	draw: function() {
		var meta = this.getMeta();
		var elements = meta.data || [];
		var ilen = elements.length;
		var i = 0;

		if (meta.dataset) {
			meta.dataset.draw();
		}

		for (; i < ilen; ++i) {
			elements[i].draw();
		}
	},

	/**
	 * Returns a set of predefined style properties that should be used to represent the dataset
	 * or the data if the index is specified
	 * @param {number} index - data index
	 * @return {IStyleInterface} style object
	 */
	getStyle: function(index) {
		var me = this;
		var meta = me.getMeta();
		var dataset = meta.dataset;
		var style;

		me._configure();
		if (dataset && index === undefined) {
			style = me._resolveDatasetElementOptions(dataset || {});
		} else {
			index = index || 0;
			style = me._resolveDataElementOptions(meta.data[index] || {}, index);
		}

		if (style.fill === false || style.fill === null) {
			style.backgroundColor = style.borderColor;
		}

		return style;
	},

	/**
	 * @private
	 */
	_resolveDatasetElementOptions: function(element, hover) {
		var me = this;
		var chart = me.chart;
		var datasetOpts = me._config;
		var custom = element.custom || {};
		var options = chart.options.elements[me.datasetElementType.prototype._type] || {};
		var elementOptions = me._datasetElementOptions;
		var values = {};
		var i, ilen, key, readKey;

		// Scriptable options
		var context = {
			chart: chart,
			dataset: me.getDataset(),
			datasetIndex: me.index,
			hover: hover
		};

		for (i = 0, ilen = elementOptions.length; i < ilen; ++i) {
			key = elementOptions[i];
			readKey = hover ? 'hover' + key.charAt(0).toUpperCase() + key.slice(1) : key;
			values[key] = resolve([
				custom[readKey],
				datasetOpts[readKey],
				options[readKey]
			], context);
		}

		return values;
	},

	/**
	 * @private
	 */
	_resolveDataElementOptions: function(element, index) {
		var me = this;
		var custom = element && element.custom;
		var cached = me._cachedDataOpts;
		if (cached && !custom) {
			return cached;
		}
		var chart = me.chart;
		var datasetOpts = me._config;
		var options = chart.options.elements[me.dataElementType.prototype._type] || {};
		var elementOptions = me._dataElementOptions;
		var values = {};

		// Scriptable options
		var context = {
			chart: chart,
			dataIndex: index,
			dataset: me.getDataset(),
			datasetIndex: me.index
		};

		// `resolve` sets cacheable to `false` if any option is indexed or scripted
		var info = {cacheable: !custom};

		var keys, i, ilen, key;

		custom = custom || {};

		if (helpers$1.isArray(elementOptions)) {
			for (i = 0, ilen = elementOptions.length; i < ilen; ++i) {
				key = elementOptions[i];
				values[key] = resolve([
					custom[key],
					datasetOpts[key],
					options[key]
				], context, index, info);
			}
		} else {
			keys = Object.keys(elementOptions);
			for (i = 0, ilen = keys.length; i < ilen; ++i) {
				key = keys[i];
				values[key] = resolve([
					custom[key],
					datasetOpts[elementOptions[key]],
					datasetOpts[key],
					options[key]
				], context, index, info);
			}
		}

		if (info.cacheable) {
			me._cachedDataOpts = Object.freeze(values);
		}

		return values;
	},

	removeHoverStyle: function(element) {
		helpers$1.merge(element._model, element.$previousStyle || {});
		delete element.$previousStyle;
	},

	setHoverStyle: function(element) {
		var dataset = this.chart.data.datasets[element._datasetIndex];
		var index = element._index;
		var custom = element.custom || {};
		var model = element._model;
		var getHoverColor = helpers$1.getHoverColor;

		element.$previousStyle = {
			backgroundColor: model.backgroundColor,
			borderColor: model.borderColor,
			borderWidth: model.borderWidth
		};

		model.backgroundColor = resolve([custom.hoverBackgroundColor, dataset.hoverBackgroundColor, getHoverColor(model.backgroundColor)], undefined, index);
		model.borderColor = resolve([custom.hoverBorderColor, dataset.hoverBorderColor, getHoverColor(model.borderColor)], undefined, index);
		model.borderWidth = resolve([custom.hoverBorderWidth, dataset.hoverBorderWidth, model.borderWidth], undefined, index);
	},

	/**
	 * @private
	 */
	_removeDatasetHoverStyle: function() {
		var element = this.getMeta().dataset;

		if (element) {
			this.removeHoverStyle(element);
		}
	},

	/**
	 * @private
	 */
	_setDatasetHoverStyle: function() {
		var element = this.getMeta().dataset;
		var prev = {};
		var i, ilen, key, keys, hoverOptions, model;

		if (!element) {
			return;
		}

		model = element._model;
		hoverOptions = this._resolveDatasetElementOptions(element, true);

		keys = Object.keys(hoverOptions);
		for (i = 0, ilen = keys.length; i < ilen; ++i) {
			key = keys[i];
			prev[key] = model[key];
			model[key] = hoverOptions[key];
		}

		element.$previousStyle = prev;
	},

	/**
	 * @private
	 */
	resyncElements: function() {
		var me = this;
		var meta = me.getMeta();
		var data = me.getDataset().data;
		var numMeta = meta.data.length;
		var numData = data.length;

		if (numData < numMeta) {
			meta.data.splice(numData, numMeta - numData);
		} else if (numData > numMeta) {
			me.insertElements(numMeta, numData - numMeta);
		}
	},

	/**
	 * @private
	 */
	insertElements: function(start, count) {
		for (var i = 0; i < count; ++i) {
			this.addElementAndReset(start + i);
		}
	},

	/**
	 * @private
	 */
	onDataPush: function() {
		var count = arguments.length;
		this.insertElements(this.getDataset().data.length - count, count);
	},

	/**
	 * @private
	 */
	onDataPop: function() {
		this.getMeta().data.pop();
	},

	/**
	 * @private
	 */
	onDataShift: function() {
		this.getMeta().data.shift();
	},

	/**
	 * @private
	 */
	onDataSplice: function(start, count) {
		this.getMeta().data.splice(start, count);
		this.insertElements(start, arguments.length - 2);
	},

	/**
	 * @private
	 */
	onDataUnshift: function() {
		this.insertElements(0, arguments.length);
	}
});

DatasetController.extend = helpers$1.inherits;

var core_datasetController = DatasetController;

var TAU = Math.PI * 2;

core_defaults._set('global', {
	elements: {
		arc: {
			backgroundColor: core_defaults.global.defaultColor,
			borderColor: '#fff',
			borderWidth: 2,
			borderAlign: 'center'
		}
	}
});

function clipArc(ctx, arc) {
	var startAngle = arc.startAngle;
	var endAngle = arc.endAngle;
	var pixelMargin = arc.pixelMargin;
	var angleMargin = pixelMargin / arc.outerRadius;
	var x = arc.x;
	var y = arc.y;

	// Draw an inner border by cliping the arc and drawing a double-width border
	// Enlarge the clipping arc by 0.33 pixels to eliminate glitches between borders
	ctx.beginPath();
	ctx.arc(x, y, arc.outerRadius, startAngle - angleMargin, endAngle + angleMargin);
	if (arc.innerRadius > pixelMargin) {
		angleMargin = pixelMargin / arc.innerRadius;
		ctx.arc(x, y, arc.innerRadius - pixelMargin, endAngle + angleMargin, startAngle - angleMargin, true);
	} else {
		ctx.arc(x, y, pixelMargin, endAngle + Math.PI / 2, startAngle - Math.PI / 2);
	}
	ctx.closePath();
	ctx.clip();
}

function drawFullCircleBorders(ctx, vm, arc, inner) {
	var endAngle = arc.endAngle;
	var i;

	if (inner) {
		arc.endAngle = arc.startAngle + TAU;
		clipArc(ctx, arc);
		arc.endAngle = endAngle;
		if (arc.endAngle === arc.startAngle && arc.fullCircles) {
			arc.endAngle += TAU;
			arc.fullCircles--;
		}
	}

	ctx.beginPath();
	ctx.arc(arc.x, arc.y, arc.innerRadius, arc.startAngle + TAU, arc.startAngle, true);
	for (i = 0; i < arc.fullCircles; ++i) {
		ctx.stroke();
	}

	ctx.beginPath();
	ctx.arc(arc.x, arc.y, vm.outerRadius, arc.startAngle, arc.startAngle + TAU);
	for (i = 0; i < arc.fullCircles; ++i) {
		ctx.stroke();
	}
}

function drawBorder(ctx, vm, arc) {
	var inner = vm.borderAlign === 'inner';

	if (inner) {
		ctx.lineWidth = vm.borderWidth * 2;
		ctx.lineJoin = 'round';
	} else {
		ctx.lineWidth = vm.borderWidth;
		ctx.lineJoin = 'bevel';
	}

	if (arc.fullCircles) {
		drawFullCircleBorders(ctx, vm, arc, inner);
	}

	if (inner) {
		clipArc(ctx, arc);
	}

	ctx.beginPath();
	ctx.arc(arc.x, arc.y, vm.outerRadius, arc.startAngle, arc.endAngle);
	ctx.arc(arc.x, arc.y, arc.innerRadius, arc.endAngle, arc.startAngle, true);
	ctx.closePath();
	ctx.stroke();
}

var element_arc = core_element.extend({
	_type: 'arc',

	inLabelRange: function(mouseX) {
		var vm = this._view;

		if (vm) {
			return (Math.pow(mouseX - vm.x, 2) < Math.pow(vm.radius + vm.hoverRadius, 2));
		}
		return false;
	},

	inRange: function(chartX, chartY) {
		var vm = this._view;

		if (vm) {
			var pointRelativePosition = helpers$1.getAngleFromPoint(vm, {x: chartX, y: chartY});
			var angle = pointRelativePosition.angle;
			var distance = pointRelativePosition.distance;

			// Sanitise angle range
			var startAngle = vm.startAngle;
			var endAngle = vm.endAngle;
			while (endAngle < startAngle) {
				endAngle += TAU;
			}
			while (angle > endAngle) {
				angle -= TAU;
			}
			while (angle < startAngle) {
				angle += TAU;
			}

			// Check if within the range of the open/close angle
			var betweenAngles = (angle >= startAngle && angle <= endAngle);
			var withinRadius = (distance >= vm.innerRadius && distance <= vm.outerRadius);

			return (betweenAngles && withinRadius);
		}
		return false;
	},

	getCenterPoint: function() {
		var vm = this._view;
		var halfAngle = (vm.startAngle + vm.endAngle) / 2;
		var halfRadius = (vm.innerRadius + vm.outerRadius) / 2;
		return {
			x: vm.x + Math.cos(halfAngle) * halfRadius,
			y: vm.y + Math.sin(halfAngle) * halfRadius
		};
	},

	getArea: function() {
		var vm = this._view;
		return Math.PI * ((vm.endAngle - vm.startAngle) / (2 * Math.PI)) * (Math.pow(vm.outerRadius, 2) - Math.pow(vm.innerRadius, 2));
	},

	tooltipPosition: function() {
		var vm = this._view;
		var centreAngle = vm.startAngle + ((vm.endAngle - vm.startAngle) / 2);
		var rangeFromCentre = (vm.outerRadius - vm.innerRadius) / 2 + vm.innerRadius;

		return {
			x: vm.x + (Math.cos(centreAngle) * rangeFromCentre),
			y: vm.y + (Math.sin(centreAngle) * rangeFromCentre)
		};
	},

	draw: function() {
		var ctx = this._chart.ctx;
		var vm = this._view;
		var pixelMargin = (vm.borderAlign === 'inner') ? 0.33 : 0;
		var arc = {
			x: vm.x,
			y: vm.y,
			innerRadius: vm.innerRadius,
			outerRadius: Math.max(vm.outerRadius - pixelMargin, 0),
			pixelMargin: pixelMargin,
			startAngle: vm.startAngle,
			endAngle: vm.endAngle,
			fullCircles: Math.floor(vm.circumference / TAU)
		};
		var i;

		ctx.save();

		ctx.fillStyle = vm.backgroundColor;
		ctx.strokeStyle = vm.borderColor;

		if (arc.fullCircles) {
			arc.endAngle = arc.startAngle + TAU;
			ctx.beginPath();
			ctx.arc(arc.x, arc.y, arc.outerRadius, arc.startAngle, arc.endAngle);
			ctx.arc(arc.x, arc.y, arc.innerRadius, arc.endAngle, arc.startAngle, true);
			ctx.closePath();
			for (i = 0; i < arc.fullCircles; ++i) {
				ctx.fill();
			}
			arc.endAngle = arc.startAngle + vm.circumference % TAU;
		}

		ctx.beginPath();
		ctx.arc(arc.x, arc.y, arc.outerRadius, arc.startAngle, arc.endAngle);
		ctx.arc(arc.x, arc.y, arc.innerRadius, arc.endAngle, arc.startAngle, true);
		ctx.closePath();
		ctx.fill();

		if (vm.borderWidth) {
			drawBorder(ctx, vm, arc);
		}

		ctx.restore();
	}
});

var valueOrDefault$1 = helpers$1.valueOrDefault;

var defaultColor = core_defaults.global.defaultColor;

core_defaults._set('global', {
	elements: {
		line: {
			tension: 0.4,
			backgroundColor: defaultColor,
			borderWidth: 3,
			borderColor: defaultColor,
			borderCapStyle: 'butt',
			borderDash: [],
			borderDashOffset: 0.0,
			borderJoinStyle: 'miter',
			capBezierPoints: true,
			fill: true, // do we fill in the area between the line and its base axis
		}
	}
});

var element_line = core_element.extend({
	_type: 'line',

	draw: function() {
		var me = this;
		var vm = me._view;
		var ctx = me._chart.ctx;
		var spanGaps = vm.spanGaps;
		var points = me._children.slice(); // clone array
		var globalDefaults = core_defaults.global;
		var globalOptionLineElements = globalDefaults.elements.line;
		var lastDrawnIndex = -1;
		var closePath = me._loop;
		var index, previous, currentVM;

		if (!points.length) {
			return;
		}

		if (me._loop) {
			for (index = 0; index < points.length; ++index) {
				previous = helpers$1.previousItem(points, index);
				// If the line has an open path, shift the point array
				if (!points[index]._view.skip && previous._view.skip) {
					points = points.slice(index).concat(points.slice(0, index));
					closePath = spanGaps;
					break;
				}
			}
			// If the line has a close path, add the first point again
			if (closePath) {
				points.push(points[0]);
			}
		}

		ctx.save();

		// Stroke Line Options
		ctx.lineCap = vm.borderCapStyle || globalOptionLineElements.borderCapStyle;

		// IE 9 and 10 do not support line dash
		if (ctx.setLineDash) {
			ctx.setLineDash(vm.borderDash || globalOptionLineElements.borderDash);
		}

		ctx.lineDashOffset = valueOrDefault$1(vm.borderDashOffset, globalOptionLineElements.borderDashOffset);
		ctx.lineJoin = vm.borderJoinStyle || globalOptionLineElements.borderJoinStyle;
		ctx.lineWidth = valueOrDefault$1(vm.borderWidth, globalOptionLineElements.borderWidth);
		ctx.strokeStyle = vm.borderColor || globalDefaults.defaultColor;

		// Stroke Line
		ctx.beginPath();

		// First point moves to it's starting position no matter what
		currentVM = points[0]._view;
		if (!currentVM.skip) {
			ctx.moveTo(currentVM.x, currentVM.y);
			lastDrawnIndex = 0;
		}

		for (index = 1; index < points.length; ++index) {
			currentVM = points[index]._view;
			previous = lastDrawnIndex === -1 ? helpers$1.previousItem(points, index) : points[lastDrawnIndex];

			if (!currentVM.skip) {
				if ((lastDrawnIndex !== (index - 1) && !spanGaps) || lastDrawnIndex === -1) {
					// There was a gap and this is the first point after the gap
					ctx.moveTo(currentVM.x, currentVM.y);
				} else {
					// Line to next point
					helpers$1.canvas.lineTo(ctx, previous._view, currentVM);
				}
				lastDrawnIndex = index;
			}
		}

		if (closePath) {
			ctx.closePath();
		}

		ctx.stroke();
		ctx.restore();
	}
});

var valueOrDefault$2 = helpers$1.valueOrDefault;

var defaultColor$1 = core_defaults.global.defaultColor;

core_defaults._set('global', {
	elements: {
		point: {
			radius: 3,
			pointStyle: 'circle',
			backgroundColor: defaultColor$1,
			borderColor: defaultColor$1,
			borderWidth: 1,
			// Hover
			hitRadius: 1,
			hoverRadius: 4,
			hoverBorderWidth: 1
		}
	}
});

function xRange(mouseX) {
	var vm = this._view;
	return vm ? (Math.abs(mouseX - vm.x) < vm.radius + vm.hitRadius) : false;
}

function yRange(mouseY) {
	var vm = this._view;
	return vm ? (Math.abs(mouseY - vm.y) < vm.radius + vm.hitRadius) : false;
}

var element_point = core_element.extend({
	_type: 'point',

	inRange: function(mouseX, mouseY) {
		var vm = this._view;
		return vm ? ((Math.pow(mouseX - vm.x, 2) + Math.pow(mouseY - vm.y, 2)) < Math.pow(vm.hitRadius + vm.radius, 2)) : false;
	},

	inLabelRange: xRange,
	inXRange: xRange,
	inYRange: yRange,

	getCenterPoint: function() {
		var vm = this._view;
		return {
			x: vm.x,
			y: vm.y
		};
	},

	getArea: function() {
		return Math.PI * Math.pow(this._view.radius, 2);
	},

	tooltipPosition: function() {
		var vm = this._view;
		return {
			x: vm.x,
			y: vm.y,
			padding: vm.radius + vm.borderWidth
		};
	},

	draw: function(chartArea) {
		var vm = this._view;
		var ctx = this._chart.ctx;
		var pointStyle = vm.pointStyle;
		var rotation = vm.rotation;
		var radius = vm.radius;
		var x = vm.x;
		var y = vm.y;
		var globalDefaults = core_defaults.global;
		var defaultColor = globalDefaults.defaultColor; // eslint-disable-line no-shadow

		if (vm.skip) {
			return;
		}

		// Clipping for Points.
		if (chartArea === undefined || helpers$1.canvas._isPointInArea(vm, chartArea)) {
			ctx.strokeStyle = vm.borderColor || defaultColor;
			ctx.lineWidth = valueOrDefault$2(vm.borderWidth, globalDefaults.elements.point.borderWidth);
			ctx.fillStyle = vm.backgroundColor || defaultColor;
			helpers$1.canvas.drawPoint(ctx, pointStyle, radius, x, y, rotation);
		}
	}
});

var defaultColor$2 = core_defaults.global.defaultColor;

core_defaults._set('global', {
	elements: {
		rectangle: {
			backgroundColor: defaultColor$2,
			borderColor: defaultColor$2,
			borderSkipped: 'bottom',
			borderWidth: 0
		}
	}
});

function isVertical(vm) {
	return vm && vm.width !== undefined;
}

/**
 * Helper function to get the bounds of the bar regardless of the orientation
 * @param bar {Chart.Element.Rectangle} the bar
 * @return {Bounds} bounds of the bar
 * @private
 */
function getBarBounds(vm) {
	var x1, x2, y1, y2, half;

	if (isVertical(vm)) {
		half = vm.width / 2;
		x1 = vm.x - half;
		x2 = vm.x + half;
		y1 = Math.min(vm.y, vm.base);
		y2 = Math.max(vm.y, vm.base);
	} else {
		half = vm.height / 2;
		x1 = Math.min(vm.x, vm.base);
		x2 = Math.max(vm.x, vm.base);
		y1 = vm.y - half;
		y2 = vm.y + half;
	}

	return {
		left: x1,
		top: y1,
		right: x2,
		bottom: y2
	};
}

function swap(orig, v1, v2) {
	return orig === v1 ? v2 : orig === v2 ? v1 : orig;
}

function parseBorderSkipped(vm) {
	var edge = vm.borderSkipped;
	var res = {};

	if (!edge) {
		return res;
	}

	if (vm.horizontal) {
		if (vm.base > vm.x) {
			edge = swap(edge, 'left', 'right');
		}
	} else if (vm.base < vm.y) {
		edge = swap(edge, 'bottom', 'top');
	}

	res[edge] = true;
	return res;
}

function parseBorderWidth(vm, maxW, maxH) {
	var value = vm.borderWidth;
	var skip = parseBorderSkipped(vm);
	var t, r, b, l;

	if (helpers$1.isObject(value)) {
		t = +value.top || 0;
		r = +value.right || 0;
		b = +value.bottom || 0;
		l = +value.left || 0;
	} else {
		t = r = b = l = +value || 0;
	}

	return {
		t: skip.top || (t < 0) ? 0 : t > maxH ? maxH : t,
		r: skip.right || (r < 0) ? 0 : r > maxW ? maxW : r,
		b: skip.bottom || (b < 0) ? 0 : b > maxH ? maxH : b,
		l: skip.left || (l < 0) ? 0 : l > maxW ? maxW : l
	};
}

function boundingRects(vm) {
	var bounds = getBarBounds(vm);
	var width = bounds.right - bounds.left;
	var height = bounds.bottom - bounds.top;
	var border = parseBorderWidth(vm, width / 2, height / 2);

	return {
		outer: {
			x: bounds.left,
			y: bounds.top,
			w: width,
			h: height
		},
		inner: {
			x: bounds.left + border.l,
			y: bounds.top + border.t,
			w: width - border.l - border.r,
			h: height - border.t - border.b
		}
	};
}

function inRange(vm, x, y) {
	var skipX = x === null;
	var skipY = y === null;
	var bounds = !vm || (skipX && skipY) ? false : getBarBounds(vm);

	return bounds
		&& (skipX || x >= bounds.left && x <= bounds.right)
		&& (skipY || y >= bounds.top && y <= bounds.bottom);
}

var element_rectangle = core_element.extend({
	_type: 'rectangle',

	draw: function() {
		var ctx = this._chart.ctx;
		var vm = this._view;
		var rects = boundingRects(vm);
		var outer = rects.outer;
		var inner = rects.inner;

		ctx.fillStyle = vm.backgroundColor;
		ctx.fillRect(outer.x, outer.y, outer.w, outer.h);

		if (outer.w === inner.w && outer.h === inner.h) {
			return;
		}

		ctx.save();
		ctx.beginPath();
		ctx.rect(outer.x, outer.y, outer.w, outer.h);
		ctx.clip();
		ctx.fillStyle = vm.borderColor;
		ctx.rect(inner.x, inner.y, inner.w, inner.h);
		ctx.fill('evenodd');
		ctx.restore();
	},

	height: function() {
		var vm = this._view;
		return vm.base - vm.y;
	},

	inRange: function(mouseX, mouseY) {
		return inRange(this._view, mouseX, mouseY);
	},

	inLabelRange: function(mouseX, mouseY) {
		var vm = this._view;
		return isVertical(vm)
			? inRange(vm, mouseX, null)
			: inRange(vm, null, mouseY);
	},

	inXRange: function(mouseX) {
		return inRange(this._view, mouseX, null);
	},

	inYRange: function(mouseY) {
		return inRange(this._view, null, mouseY);
	},

	getCenterPoint: function() {
		var vm = this._view;
		var x, y;
		if (isVertical(vm)) {
			x = vm.x;
			y = (vm.y + vm.base) / 2;
		} else {
			x = (vm.x + vm.base) / 2;
			y = vm.y;
		}

		return {x: x, y: y};
	},

	getArea: function() {
		var vm = this._view;

		return isVertical(vm)
			? vm.width * Math.abs(vm.y - vm.base)
			: vm.height * Math.abs(vm.x - vm.base);
	},

	tooltipPosition: function() {
		var vm = this._view;
		return {
			x: vm.x,
			y: vm.y
		};
	}
});

var elements = {};
var Arc = element_arc;
var Line = element_line;
var Point = element_point;
var Rectangle = element_rectangle;
elements.Arc = Arc;
elements.Line = Line;
elements.Point = Point;
elements.Rectangle = Rectangle;

var deprecated = helpers$1._deprecated;
var valueOrDefault$3 = helpers$1.valueOrDefault;

core_defaults._set('bar', {
	hover: {
		mode: 'label'
	},

	scales: {
		xAxes: [{
			type: 'category',
			offset: true,
			gridLines: {
				offsetGridLines: true
			}
		}],

		yAxes: [{
			type: 'linear'
		}]
	}
});

core_defaults._set('global', {
	datasets: {
		bar: {
			categoryPercentage: 0.8,
			barPercentage: 0.9
		}
	}
});

/**
 * Computes the "optimal" sample size to maintain bars equally sized while preventing overlap.
 * @private
 */
function computeMinSampleSize(scale, pixels) {
	var min = scale._length;
	var prev, curr, i, ilen;

	for (i = 1, ilen = pixels.length; i < ilen; ++i) {
		min = Math.min(min, Math.abs(pixels[i] - pixels[i - 1]));
	}

	for (i = 0, ilen = scale.getTicks().length; i < ilen; ++i) {
		curr = scale.getPixelForTick(i);
		min = i > 0 ? Math.min(min, Math.abs(curr - prev)) : min;
		prev = curr;
	}

	return min;
}

/**
 * Computes an "ideal" category based on the absolute bar thickness or, if undefined or null,
 * uses the smallest interval (see computeMinSampleSize) that prevents bar overlapping. This
 * mode currently always generates bars equally sized (until we introduce scriptable options?).
 * @private
 */
function computeFitCategoryTraits(index, ruler, options) {
	var thickness = options.barThickness;
	var count = ruler.stackCount;
	var curr = ruler.pixels[index];
	var min = helpers$1.isNullOrUndef(thickness)
		? computeMinSampleSize(ruler.scale, ruler.pixels)
		: -1;
	var size, ratio;

	if (helpers$1.isNullOrUndef(thickness)) {
		size = min * options.categoryPercentage;
		ratio = options.barPercentage;
	} else {
		// When bar thickness is enforced, category and bar percentages are ignored.
		// Note(SB): we could add support for relative bar thickness (e.g. barThickness: '50%')
		// and deprecate barPercentage since this value is ignored when thickness is absolute.
		size = thickness * count;
		ratio = 1;
	}

	return {
		chunk: size / count,
		ratio: ratio,
		start: curr - (size / 2)
	};
}

/**
 * Computes an "optimal" category that globally arranges bars side by side (no gap when
 * percentage options are 1), based on the previous and following categories. This mode
 * generates bars with different widths when data are not evenly spaced.
 * @private
 */
function computeFlexCategoryTraits(index, ruler, options) {
	var pixels = ruler.pixels;
	var curr = pixels[index];
	var prev = index > 0 ? pixels[index - 1] : null;
	var next = index < pixels.length - 1 ? pixels[index + 1] : null;
	var percent = options.categoryPercentage;
	var start, size;

	if (prev === null) {
		// first data: its size is double based on the next point or,
		// if it's also the last data, we use the scale size.
		prev = curr - (next === null ? ruler.end - ruler.start : next - curr);
	}

	if (next === null) {
		// last data: its size is also double based on the previous point.
		next = curr + curr - prev;
	}

	start = curr - (curr - Math.min(prev, next)) / 2 * percent;
	size = Math.abs(next - prev) / 2 * percent;

	return {
		chunk: size / ruler.stackCount,
		ratio: options.barPercentage,
		start: start
	};
}

var controller_bar = core_datasetController.extend({

	dataElementType: elements.Rectangle,

	/**
	 * @private
	 */
	_dataElementOptions: [
		'backgroundColor',
		'borderColor',
		'borderSkipped',
		'borderWidth',
		'barPercentage',
		'barThickness',
		'categoryPercentage',
		'maxBarThickness',
		'minBarLength'
	],

	initialize: function() {
		var me = this;
		var meta, scaleOpts;

		core_datasetController.prototype.initialize.apply(me, arguments);

		meta = me.getMeta();
		meta.stack = me.getDataset().stack;
		meta.bar = true;

		scaleOpts = me._getIndexScale().options;
		deprecated('bar chart', scaleOpts.barPercentage, 'scales.[x/y]Axes.barPercentage', 'dataset.barPercentage');
		deprecated('bar chart', scaleOpts.barThickness, 'scales.[x/y]Axes.barThickness', 'dataset.barThickness');
		deprecated('bar chart', scaleOpts.categoryPercentage, 'scales.[x/y]Axes.categoryPercentage', 'dataset.categoryPercentage');
		deprecated('bar chart', me._getValueScale().options.minBarLength, 'scales.[x/y]Axes.minBarLength', 'dataset.minBarLength');
		deprecated('bar chart', scaleOpts.maxBarThickness, 'scales.[x/y]Axes.maxBarThickness', 'dataset.maxBarThickness');
	},

	update: function(reset) {
		var me = this;
		var rects = me.getMeta().data;
		var i, ilen;

		me._ruler = me.getRuler();

		for (i = 0, ilen = rects.length; i < ilen; ++i) {
			me.updateElement(rects[i], i, reset);
		}
	},

	updateElement: function(rectangle, index, reset) {
		var me = this;
		var meta = me.getMeta();
		var dataset = me.getDataset();
		var options = me._resolveDataElementOptions(rectangle, index);

		rectangle._xScale = me.getScaleForId(meta.xAxisID);
		rectangle._yScale = me.getScaleForId(meta.yAxisID);
		rectangle._datasetIndex = me.index;
		rectangle._index = index;
		rectangle._model = {
			backgroundColor: options.backgroundColor,
			borderColor: options.borderColor,
			borderSkipped: options.borderSkipped,
			borderWidth: options.borderWidth,
			datasetLabel: dataset.label,
			label: me.chart.data.labels[index]
		};

		if (helpers$1.isArray(dataset.data[index])) {
			rectangle._model.borderSkipped = null;
		}

		me._updateElementGeometry(rectangle, index, reset, options);

		rectangle.pivot();
	},

	/**
	 * @private
	 */
	_updateElementGeometry: function(rectangle, index, reset, options) {
		var me = this;
		var model = rectangle._model;
		var vscale = me._getValueScale();
		var base = vscale.getBasePixel();
		var horizontal = vscale.isHorizontal();
		var ruler = me._ruler || me.getRuler();
		var vpixels = me.calculateBarValuePixels(me.index, index, options);
		var ipixels = me.calculateBarIndexPixels(me.index, index, ruler, options);

		model.horizontal = horizontal;
		model.base = reset ? base : vpixels.base;
		model.x = horizontal ? reset ? base : vpixels.head : ipixels.center;
		model.y = horizontal ? ipixels.center : reset ? base : vpixels.head;
		model.height = horizontal ? ipixels.size : undefined;
		model.width = horizontal ? undefined : ipixels.size;
	},

	/**
	 * Returns the stacks based on groups and bar visibility.
	 * @param {number} [last] - The dataset index
	 * @returns {string[]} The list of stack IDs
	 * @private
	 */
	_getStacks: function(last) {
		var me = this;
		var scale = me._getIndexScale();
		var metasets = scale._getMatchingVisibleMetas(me._type);
		var stacked = scale.options.stacked;
		var ilen = metasets.length;
		var stacks = [];
		var i, meta;

		for (i = 0; i < ilen; ++i) {
			meta = metasets[i];
			// stacked   | meta.stack
			//           | found | not found | undefined
			// false     |   x   |     x     |     x
			// true      |       |     x     |
			// undefined |       |     x     |     x
			if (stacked === false || stacks.indexOf(meta.stack) === -1 ||
				(stacked === undefined && meta.stack === undefined)) {
				stacks.push(meta.stack);
			}
			if (meta.index === last) {
				break;
			}
		}

		return stacks;
	},

	/**
	 * Returns the effective number of stacks based on groups and bar visibility.
	 * @private
	 */
	getStackCount: function() {
		return this._getStacks().length;
	},

	/**
	 * Returns the stack index for the given dataset based on groups and bar visibility.
	 * @param {number} [datasetIndex] - The dataset index
	 * @param {string} [name] - The stack name to find
	 * @returns {number} The stack index
	 * @private
	 */
	getStackIndex: function(datasetIndex, name) {
		var stacks = this._getStacks(datasetIndex);
		var index = (name !== undefined)
			? stacks.indexOf(name)
			: -1; // indexOf returns -1 if element is not present

		return (index === -1)
			? stacks.length - 1
			: index;
	},

	/**
	 * @private
	 */
	getRuler: function() {
		var me = this;
		var scale = me._getIndexScale();
		var pixels = [];
		var i, ilen;

		for (i = 0, ilen = me.getMeta().data.length; i < ilen; ++i) {
			pixels.push(scale.getPixelForValue(null, i, me.index));
		}

		return {
			pixels: pixels,
			start: scale._startPixel,
			end: scale._endPixel,
			stackCount: me.getStackCount(),
			scale: scale
		};
	},

	/**
	 * Note: pixel values are not clamped to the scale area.
	 * @private
	 */
	calculateBarValuePixels: function(datasetIndex, index, options) {
		var me = this;
		var chart = me.chart;
		var scale = me._getValueScale();
		var isHorizontal = scale.isHorizontal();
		var datasets = chart.data.datasets;
		var metasets = scale._getMatchingVisibleMetas(me._type);
		var value = scale._parseValue(datasets[datasetIndex].data[index]);
		var minBarLength = options.minBarLength;
		var stacked = scale.options.stacked;
		var stack = me.getMeta().stack;
		var start = value.start === undefined ? 0 : value.max >= 0 && value.min >= 0 ? value.min : value.max;
		var length = value.start === undefined ? value.end : value.max >= 0 && value.min >= 0 ? value.max - value.min : value.min - value.max;
		var ilen = metasets.length;
		var i, imeta, ivalue, base, head, size, stackLength;

		if (stacked || (stacked === undefined && stack !== undefined)) {
			for (i = 0; i < ilen; ++i) {
				imeta = metasets[i];

				if (imeta.index === datasetIndex) {
					break;
				}

				if (imeta.stack === stack) {
					stackLength = scale._parseValue(datasets[imeta.index].data[index]);
					ivalue = stackLength.start === undefined ? stackLength.end : stackLength.min >= 0 && stackLength.max >= 0 ? stackLength.max : stackLength.min;

					if ((value.min < 0 && ivalue < 0) || (value.max >= 0 && ivalue > 0)) {
						start += ivalue;
					}
				}
			}
		}

		base = scale.getPixelForValue(start);
		head = scale.getPixelForValue(start + length);
		size = head - base;

		if (minBarLength !== undefined && Math.abs(size) < minBarLength) {
			size = minBarLength;
			if (length >= 0 && !isHorizontal || length < 0 && isHorizontal) {
				head = base - minBarLength;
			} else {
				head = base + minBarLength;
			}
		}

		return {
			size: size,
			base: base,
			head: head,
			center: head + size / 2
		};
	},

	/**
	 * @private
	 */
	calculateBarIndexPixels: function(datasetIndex, index, ruler, options) {
		var me = this;
		var range = options.barThickness === 'flex'
			? computeFlexCategoryTraits(index, ruler, options)
			: computeFitCategoryTraits(index, ruler, options);

		var stackIndex = me.getStackIndex(datasetIndex, me.getMeta().stack);
		var center = range.start + (range.chunk * stackIndex) + (range.chunk / 2);
		var size = Math.min(
			valueOrDefault$3(options.maxBarThickness, Infinity),
			range.chunk * range.ratio);

		return {
			base: center - size / 2,
			head: center + size / 2,
			center: center,
			size: size
		};
	},

	draw: function() {
		var me = this;
		var chart = me.chart;
		var scale = me._getValueScale();
		var rects = me.getMeta().data;
		var dataset = me.getDataset();
		var ilen = rects.length;
		var i = 0;

		helpers$1.canvas.clipArea(chart.ctx, chart.chartArea);

		for (; i < ilen; ++i) {
			var val = scale._parseValue(dataset.data[i]);
			if (!isNaN(val.min) && !isNaN(val.max)) {
				rects[i].draw();
			}
		}

		helpers$1.canvas.unclipArea(chart.ctx);
	},

	/**
	 * @private
	 */
	_resolveDataElementOptions: function() {
		var me = this;
		var values = helpers$1.extend({}, core_datasetController.prototype._resolveDataElementOptions.apply(me, arguments));
		var indexOpts = me._getIndexScale().options;
		var valueOpts = me._getValueScale().options;

		values.barPercentage = valueOrDefault$3(indexOpts.barPercentage, values.barPercentage);
		values.barThickness = valueOrDefault$3(indexOpts.barThickness, values.barThickness);
		values.categoryPercentage = valueOrDefault$3(indexOpts.categoryPercentage, values.categoryPercentage);
		values.maxBarThickness = valueOrDefault$3(indexOpts.maxBarThickness, values.maxBarThickness);
		values.minBarLength = valueOrDefault$3(valueOpts.minBarLength, values.minBarLength);

		return values;
	}

});

var valueOrDefault$4 = helpers$1.valueOrDefault;
var resolve$1 = helpers$1.options.resolve;

core_defaults._set('bubble', {
	hover: {
		mode: 'single'
	},

	scales: {
		xAxes: [{
			type: 'linear', // bubble should probably use a linear scale by default
			position: 'bottom',
			id: 'x-axis-0' // need an ID so datasets can reference the scale
		}],
		yAxes: [{
			type: 'linear',
			position: 'left',
			id: 'y-axis-0'
		}]
	},

	tooltips: {
		callbacks: {
			title: function() {
				// Title doesn't make sense for scatter since we format the data as a point
				return '';
			},
			label: function(item, data) {
				var datasetLabel = data.datasets[item.datasetIndex].label || '';
				var dataPoint = data.datasets[item.datasetIndex].data[item.index];
				return datasetLabel + ': (' + item.xLabel + ', ' + item.yLabel + ', ' + dataPoint.r + ')';
			}
		}
	}
});

var controller_bubble = core_datasetController.extend({
	/**
	 * @protected
	 */
	dataElementType: elements.Point,

	/**
	 * @private
	 */
	_dataElementOptions: [
		'backgroundColor',
		'borderColor',
		'borderWidth',
		'hoverBackgroundColor',
		'hoverBorderColor',
		'hoverBorderWidth',
		'hoverRadius',
		'hitRadius',
		'pointStyle',
		'rotation'
	],

	/**
	 * @protected
	 */
	update: function(reset) {
		var me = this;
		var meta = me.getMeta();
		var points = meta.data;

		// Update Points
		helpers$1.each(points, function(point, index) {
			me.updateElement(point, index, reset);
		});
	},

	/**
	 * @protected
	 */
	updateElement: function(point, index, reset) {
		var me = this;
		var meta = me.getMeta();
		var custom = point.custom || {};
		var xScale = me.getScaleForId(meta.xAxisID);
		var yScale = me.getScaleForId(meta.yAxisID);
		var options = me._resolveDataElementOptions(point, index);
		var data = me.getDataset().data[index];
		var dsIndex = me.index;

		var x = reset ? xScale.getPixelForDecimal(0.5) : xScale.getPixelForValue(typeof data === 'object' ? data : NaN, index, dsIndex);
		var y = reset ? yScale.getBasePixel() : yScale.getPixelForValue(data, index, dsIndex);

		point._xScale = xScale;
		point._yScale = yScale;
		point._options = options;
		point._datasetIndex = dsIndex;
		point._index = index;
		point._model = {
			backgroundColor: options.backgroundColor,
			borderColor: options.borderColor,
			borderWidth: options.borderWidth,
			hitRadius: options.hitRadius,
			pointStyle: options.pointStyle,
			rotation: options.rotation,
			radius: reset ? 0 : options.radius,
			skip: custom.skip || isNaN(x) || isNaN(y),
			x: x,
			y: y,
		};

		point.pivot();
	},

	/**
	 * @protected
	 */
	setHoverStyle: function(point) {
		var model = point._model;
		var options = point._options;
		var getHoverColor = helpers$1.getHoverColor;

		point.$previousStyle = {
			backgroundColor: model.backgroundColor,
			borderColor: model.borderColor,
			borderWidth: model.borderWidth,
			radius: model.radius
		};

		model.backgroundColor = valueOrDefault$4(options.hoverBackgroundColor, getHoverColor(options.backgroundColor));
		model.borderColor = valueOrDefault$4(options.hoverBorderColor, getHoverColor(options.borderColor));
		model.borderWidth = valueOrDefault$4(options.hoverBorderWidth, options.borderWidth);
		model.radius = options.radius + options.hoverRadius;
	},

	/**
	 * @private
	 */
	_resolveDataElementOptions: function(point, index) {
		var me = this;
		var chart = me.chart;
		var dataset = me.getDataset();
		var custom = point.custom || {};
		var data = dataset.data[index] || {};
		var values = core_datasetController.prototype._resolveDataElementOptions.apply(me, arguments);

		// Scriptable options
		var context = {
			chart: chart,
			dataIndex: index,
			dataset: dataset,
			datasetIndex: me.index
		};

		// In case values were cached (and thus frozen), we need to clone the values
		if (me._cachedDataOpts === values) {
			values = helpers$1.extend({}, values);
		}

		// Custom radius resolution
		values.radius = resolve$1([
			custom.radius,
			data.r,
			me._config.radius,
			chart.options.elements.point.radius
		], context, index);

		return values;
	}
});

var valueOrDefault$5 = helpers$1.valueOrDefault;

var PI$1 = Math.PI;
var DOUBLE_PI$1 = PI$1 * 2;
var HALF_PI$1 = PI$1 / 2;

core_defaults._set('doughnut', {
	animation: {
		// Boolean - Whether we animate the rotation of the Doughnut
		animateRotate: true,
		// Boolean - Whether we animate scaling the Doughnut from the centre
		animateScale: false
	},
	hover: {
		mode: 'single'
	},
	legendCallback: function(chart) {
		var list = document.createElement('ul');
		var data = chart.data;
		var datasets = data.datasets;
		var labels = data.labels;
		var i, ilen, listItem, listItemSpan;

		list.setAttribute('class', chart.id + '-legend');
		if (datasets.length) {
			for (i = 0, ilen = datasets[0].data.length; i < ilen; ++i) {
				listItem = list.appendChild(document.createElement('li'));
				listItemSpan = listItem.appendChild(document.createElement('span'));
				listItemSpan.style.backgroundColor = datasets[0].backgroundColor[i];
				if (labels[i]) {
					listItem.appendChild(document.createTextNode(labels[i]));
				}
			}
		}

		return list.outerHTML;
	},
	legend: {
		labels: {
			generateLabels: function(chart) {
				var data = chart.data;
				if (data.labels.length && data.datasets.length) {
					return data.labels.map(function(label, i) {
						var meta = chart.getDatasetMeta(0);
						var style = meta.controller.getStyle(i);

						return {
							text: label,
							fillStyle: style.backgroundColor,
							strokeStyle: style.borderColor,
							lineWidth: style.borderWidth,
							hidden: isNaN(data.datasets[0].data[i]) || meta.data[i].hidden,

							// Extra data used for toggling the correct item
							index: i
						};
					});
				}
				return [];
			}
		},

		onClick: function(e, legendItem) {
			var index = legendItem.index;
			var chart = this.chart;
			var i, ilen, meta;

			for (i = 0, ilen = (chart.data.datasets || []).length; i < ilen; ++i) {
				meta = chart.getDatasetMeta(i);
				// toggle visibility of index if exists
				if (meta.data[index]) {
					meta.data[index].hidden = !meta.data[index].hidden;
				}
			}

			chart.update();
		}
	},

	// The percentage of the chart that we cut out of the middle.
	cutoutPercentage: 50,

	// The rotation of the chart, where the first data arc begins.
	rotation: -HALF_PI$1,

	// The total circumference of the chart.
	circumference: DOUBLE_PI$1,

	// Need to override these to give a nice default
	tooltips: {
		callbacks: {
			title: function() {
				return '';
			},
			label: function(tooltipItem, data) {
				var dataLabel = data.labels[tooltipItem.index];
				var value = ': ' + data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];

				if (helpers$1.isArray(dataLabel)) {
					// show value on first line of multiline label
					// need to clone because we are changing the value
					dataLabel = dataLabel.slice();
					dataLabel[0] += value;
				} else {
					dataLabel += value;
				}

				return dataLabel;
			}
		}
	}
});

var controller_doughnut = core_datasetController.extend({

	dataElementType: elements.Arc,

	linkScales: helpers$1.noop,

	/**
	 * @private
	 */
	_dataElementOptions: [
		'backgroundColor',
		'borderColor',
		'borderWidth',
		'borderAlign',
		'hoverBackgroundColor',
		'hoverBorderColor',
		'hoverBorderWidth',
	],

	// Get index of the dataset in relation to the visible datasets. This allows determining the inner and outer radius correctly
	getRingIndex: function(datasetIndex) {
		var ringIndex = 0;

		for (var j = 0; j < datasetIndex; ++j) {
			if (this.chart.isDatasetVisible(j)) {
				++ringIndex;
			}
		}

		return ringIndex;
	},

	update: function(reset) {
		var me = this;
		var chart = me.chart;
		var chartArea = chart.chartArea;
		var opts = chart.options;
		var ratioX = 1;
		var ratioY = 1;
		var offsetX = 0;
		var offsetY = 0;
		var meta = me.getMeta();
		var arcs = meta.data;
		var cutout = opts.cutoutPercentage / 100 || 0;
		var circumference = opts.circumference;
		var chartWeight = me._getRingWeight(me.index);
		var maxWidth, maxHeight, i, ilen;

		// If the chart's circumference isn't a full circle, calculate size as a ratio of the width/height of the arc
		if (circumference < DOUBLE_PI$1) {
			var startAngle = opts.rotation % DOUBLE_PI$1;
			startAngle += startAngle >= PI$1 ? -DOUBLE_PI$1 : startAngle < -PI$1 ? DOUBLE_PI$1 : 0;
			var endAngle = startAngle + circumference;
			var startX = Math.cos(startAngle);
			var startY = Math.sin(startAngle);
			var endX = Math.cos(endAngle);
			var endY = Math.sin(endAngle);
			var contains0 = (startAngle <= 0 && endAngle >= 0) || endAngle >= DOUBLE_PI$1;
			var contains90 = (startAngle <= HALF_PI$1 && endAngle >= HALF_PI$1) || endAngle >= DOUBLE_PI$1 + HALF_PI$1;
			var contains180 = startAngle === -PI$1 || endAngle >= PI$1;
			var contains270 = (startAngle <= -HALF_PI$1 && endAngle >= -HALF_PI$1) || endAngle >= PI$1 + HALF_PI$1;
			var minX = contains180 ? -1 : Math.min(startX, startX * cutout, endX, endX * cutout);
			var minY = contains270 ? -1 : Math.min(startY, startY * cutout, endY, endY * cutout);
			var maxX = contains0 ? 1 : Math.max(startX, startX * cutout, endX, endX * cutout);
			var maxY = contains90 ? 1 : Math.max(startY, startY * cutout, endY, endY * cutout);
			ratioX = (maxX - minX) / 2;
			ratioY = (maxY - minY) / 2;
			offsetX = -(maxX + minX) / 2;
			offsetY = -(maxY + minY) / 2;
		}

		for (i = 0, ilen = arcs.length; i < ilen; ++i) {
			arcs[i]._options = me._resolveDataElementOptions(arcs[i], i);
		}

		chart.borderWidth = me.getMaxBorderWidth();
		maxWidth = (chartArea.right - chartArea.left - chart.borderWidth) / ratioX;
		maxHeight = (chartArea.bottom - chartArea.top - chart.borderWidth) / ratioY;
		chart.outerRadius = Math.max(Math.min(maxWidth, maxHeight) / 2, 0);
		chart.innerRadius = Math.max(chart.outerRadius * cutout, 0);
		chart.radiusLength = (chart.outerRadius - chart.innerRadius) / (me._getVisibleDatasetWeightTotal() || 1);
		chart.offsetX = offsetX * chart.outerRadius;
		chart.offsetY = offsetY * chart.outerRadius;

		meta.total = me.calculateTotal();

		me.outerRadius = chart.outerRadius - chart.radiusLength * me._getRingWeightOffset(me.index);
		me.innerRadius = Math.max(me.outerRadius - chart.radiusLength * chartWeight, 0);

		for (i = 0, ilen = arcs.length; i < ilen; ++i) {
			me.updateElement(arcs[i], i, reset);
		}
	},

	updateElement: function(arc, index, reset) {
		var me = this;
		var chart = me.chart;
		var chartArea = chart.chartArea;
		var opts = chart.options;
		var animationOpts = opts.animation;
		var centerX = (chartArea.left + chartArea.right) / 2;
		var centerY = (chartArea.top + chartArea.bottom) / 2;
		var startAngle = opts.rotation; // non reset case handled later
		var endAngle = opts.rotation; // non reset case handled later
		var dataset = me.getDataset();
		var circumference = reset && animationOpts.animateRotate ? 0 : arc.hidden ? 0 : me.calculateCircumference(dataset.data[index]) * (opts.circumference / DOUBLE_PI$1);
		var innerRadius = reset && animationOpts.animateScale ? 0 : me.innerRadius;
		var outerRadius = reset && animationOpts.animateScale ? 0 : me.outerRadius;
		var options = arc._options || {};

		helpers$1.extend(arc, {
			// Utility
			_datasetIndex: me.index,
			_index: index,

			// Desired view properties
			_model: {
				backgroundColor: options.backgroundColor,
				borderColor: options.borderColor,
				borderWidth: options.borderWidth,
				borderAlign: options.borderAlign,
				x: centerX + chart.offsetX,
				y: centerY + chart.offsetY,
				startAngle: startAngle,
				endAngle: endAngle,
				circumference: circumference,
				outerRadius: outerRadius,
				innerRadius: innerRadius,
				label: helpers$1.valueAtIndexOrDefault(dataset.label, index, chart.data.labels[index])
			}
		});

		var model = arc._model;

		// Set correct angles if not resetting
		if (!reset || !animationOpts.animateRotate) {
			if (index === 0) {
				model.startAngle = opts.rotation;
			} else {
				model.startAngle = me.getMeta().data[index - 1]._model.endAngle;
			}

			model.endAngle = model.startAngle + model.circumference;
		}

		arc.pivot();
	},

	calculateTotal: function() {
		var dataset = this.getDataset();
		var meta = this.getMeta();
		var total = 0;
		var value;

		helpers$1.each(meta.data, function(element, index) {
			value = dataset.data[index];
			if (!isNaN(value) && !element.hidden) {
				total += Math.abs(value);
			}
		});

		/* if (total === 0) {
			total = NaN;
		}*/

		return total;
	},

	calculateCircumference: function(value) {
		var total = this.getMeta().total;
		if (total > 0 && !isNaN(value)) {
			return DOUBLE_PI$1 * (Math.abs(value) / total);
		}
		return 0;
	},

	// gets the max border or hover width to properly scale pie charts
	getMaxBorderWidth: function(arcs) {
		var me = this;
		var max = 0;
		var chart = me.chart;
		var i, ilen, meta, arc, controller, options, borderWidth, hoverWidth;

		if (!arcs) {
			// Find the outmost visible dataset
			for (i = 0, ilen = chart.data.datasets.length; i < ilen; ++i) {
				if (chart.isDatasetVisible(i)) {
					meta = chart.getDatasetMeta(i);
					arcs = meta.data;
					if (i !== me.index) {
						controller = meta.controller;
					}
					break;
				}
			}
		}

		if (!arcs) {
			return 0;
		}

		for (i = 0, ilen = arcs.length; i < ilen; ++i) {
			arc = arcs[i];
			if (controller) {
				controller._configure();
				options = controller._resolveDataElementOptions(arc, i);
			} else {
				options = arc._options;
			}
			if (options.borderAlign !== 'inner') {
				borderWidth = options.borderWidth;
				hoverWidth = options.hoverBorderWidth;

				max = borderWidth > max ? borderWidth : max;
				max = hoverWidth > max ? hoverWidth : max;
			}
		}
		return max;
	},

	/**
	 * @protected
	 */
	setHoverStyle: function(arc) {
		var model = arc._model;
		var options = arc._options;
		var getHoverColor = helpers$1.getHoverColor;

		arc.$previousStyle = {
			backgroundColor: model.backgroundColor,
			borderColor: model.borderColor,
			borderWidth: model.borderWidth,
		};

		model.backgroundColor = valueOrDefault$5(options.hoverBackgroundColor, getHoverColor(options.backgroundColor));
		model.borderColor = valueOrDefault$5(options.hoverBorderColor, getHoverColor(options.borderColor));
		model.borderWidth = valueOrDefault$5(options.hoverBorderWidth, options.borderWidth);
	},

	/**
	 * Get radius length offset of the dataset in relation to the visible datasets weights. This allows determining the inner and outer radius correctly
	 * @private
	 */
	_getRingWeightOffset: function(datasetIndex) {
		var ringWeightOffset = 0;

		for (var i = 0; i < datasetIndex; ++i) {
			if (this.chart.isDatasetVisible(i)) {
				ringWeightOffset += this._getRingWeight(i);
			}
		}

		return ringWeightOffset;
	},

	/**
	 * @private
	 */
	_getRingWeight: function(dataSetIndex) {
		return Math.max(valueOrDefault$5(this.chart.data.datasets[dataSetIndex].weight, 1), 0);
	},

	/**
	 * Returns the sum of all visibile data set weights.  This value can be 0.
	 * @private
	 */
	_getVisibleDatasetWeightTotal: function() {
		return this._getRingWeightOffset(this.chart.data.datasets.length);
	}
});

core_defaults._set('horizontalBar', {
	hover: {
		mode: 'index',
		axis: 'y'
	},

	scales: {
		xAxes: [{
			type: 'linear',
			position: 'bottom'
		}],

		yAxes: [{
			type: 'category',
			position: 'left',
			offset: true,
			gridLines: {
				offsetGridLines: true
			}
		}]
	},

	elements: {
		rectangle: {
			borderSkipped: 'left'
		}
	},

	tooltips: {
		mode: 'index',
		axis: 'y'
	}
});

core_defaults._set('global', {
	datasets: {
		horizontalBar: {
			categoryPercentage: 0.8,
			barPercentage: 0.9
		}
	}
});

var controller_horizontalBar = controller_bar.extend({
	/**
	 * @private
	 */
	_getValueScaleId: function() {
		return this.getMeta().xAxisID;
	},

	/**
	 * @private
	 */
	_getIndexScaleId: function() {
		return this.getMeta().yAxisID;
	}
});

var valueOrDefault$6 = helpers$1.valueOrDefault;
var resolve$2 = helpers$1.options.resolve;
var isPointInArea = helpers$1.canvas._isPointInArea;

core_defaults._set('line', {
	showLines: true,
	spanGaps: false,

	hover: {
		mode: 'label'
	},

	scales: {
		xAxes: [{
			type: 'category',
			id: 'x-axis-0'
		}],
		yAxes: [{
			type: 'linear',
			id: 'y-axis-0'
		}]
	}
});

function scaleClip(scale, halfBorderWidth) {
	var tickOpts = scale && scale.options.ticks || {};
	var reverse = tickOpts.reverse;
	var min = tickOpts.min === undefined ? halfBorderWidth : 0;
	var max = tickOpts.max === undefined ? halfBorderWidth : 0;
	return {
		start: reverse ? max : min,
		end: reverse ? min : max
	};
}

function defaultClip(xScale, yScale, borderWidth) {
	var halfBorderWidth = borderWidth / 2;
	var x = scaleClip(xScale, halfBorderWidth);
	var y = scaleClip(yScale, halfBorderWidth);

	return {
		top: y.end,
		right: x.end,
		bottom: y.start,
		left: x.start
	};
}

function toClip(value) {
	var t, r, b, l;

	if (helpers$1.isObject(value)) {
		t = value.top;
		r = value.right;
		b = value.bottom;
		l = value.left;
	} else {
		t = r = b = l = value;
	}

	return {
		top: t,
		right: r,
		bottom: b,
		left: l
	};
}


var controller_line = core_datasetController.extend({

	datasetElementType: elements.Line,

	dataElementType: elements.Point,

	/**
	 * @private
	 */
	_datasetElementOptions: [
		'backgroundColor',
		'borderCapStyle',
		'borderColor',
		'borderDash',
		'borderDashOffset',
		'borderJoinStyle',
		'borderWidth',
		'cubicInterpolationMode',
		'fill'
	],

	/**
	 * @private
	 */
	_dataElementOptions: {
		backgroundColor: 'pointBackgroundColor',
		borderColor: 'pointBorderColor',
		borderWidth: 'pointBorderWidth',
		hitRadius: 'pointHitRadius',
		hoverBackgroundColor: 'pointHoverBackgroundColor',
		hoverBorderColor: 'pointHoverBorderColor',
		hoverBorderWidth: 'pointHoverBorderWidth',
		hoverRadius: 'pointHoverRadius',
		pointStyle: 'pointStyle',
		radius: 'pointRadius',
		rotation: 'pointRotation'
	},

	update: function(reset) {
		var me = this;
		var meta = me.getMeta();
		var line = meta.dataset;
		var points = meta.data || [];
		var options = me.chart.options;
		var config = me._config;
		var showLine = me._showLine = valueOrDefault$6(config.showLine, options.showLines);
		var i, ilen;

		me._xScale = me.getScaleForId(meta.xAxisID);
		me._yScale = me.getScaleForId(meta.yAxisID);

		// Update Line
		if (showLine) {
			// Compatibility: If the properties are defined with only the old name, use those values
			if (config.tension !== undefined && config.lineTension === undefined) {
				config.lineTension = config.tension;
			}

			// Utility
			line._scale = me._yScale;
			line._datasetIndex = me.index;
			// Data
			line._children = points;
			// Model
			line._model = me._resolveDatasetElementOptions(line);

			line.pivot();
		}

		// Update Points
		for (i = 0, ilen = points.length; i < ilen; ++i) {
			me.updateElement(points[i], i, reset);
		}

		if (showLine && line._model.tension !== 0) {
			me.updateBezierControlPoints();
		}

		// Now pivot the point for animation
		for (i = 0, ilen = points.length; i < ilen; ++i) {
			points[i].pivot();
		}
	},

	updateElement: function(point, index, reset) {
		var me = this;
		var meta = me.getMeta();
		var custom = point.custom || {};
		var dataset = me.getDataset();
		var datasetIndex = me.index;
		var value = dataset.data[index];
		var xScale = me._xScale;
		var yScale = me._yScale;
		var lineModel = meta.dataset._model;
		var x, y;

		var options = me._resolveDataElementOptions(point, index);

		x = xScale.getPixelForValue(typeof value === 'object' ? value : NaN, index, datasetIndex);
		y = reset ? yScale.getBasePixel() : me.calculatePointY(value, index, datasetIndex);

		// Utility
		point._xScale = xScale;
		point._yScale = yScale;
		point._options = options;
		point._datasetIndex = datasetIndex;
		point._index = index;

		// Desired view properties
		point._model = {
			x: x,
			y: y,
			skip: custom.skip || isNaN(x) || isNaN(y),
			// Appearance
			radius: options.radius,
			pointStyle: options.pointStyle,
			rotation: options.rotation,
			backgroundColor: options.backgroundColor,
			borderColor: options.borderColor,
			borderWidth: options.borderWidth,
			tension: valueOrDefault$6(custom.tension, lineModel ? lineModel.tension : 0),
			steppedLine: lineModel ? lineModel.steppedLine : false,
			// Tooltip
			hitRadius: options.hitRadius
		};
	},

	/**
	 * @private
	 */
	_resolveDatasetElementOptions: function(element) {
		var me = this;
		var config = me._config;
		var custom = element.custom || {};
		var options = me.chart.options;
		var lineOptions = options.elements.line;
		var values = core_datasetController.prototype._resolveDatasetElementOptions.apply(me, arguments);

		// The default behavior of lines is to break at null values, according
		// to https://github.com/chartjs/Chart.js/issues/2435#issuecomment-216718158
		// This option gives lines the ability to span gaps
		values.spanGaps = valueOrDefault$6(config.spanGaps, options.spanGaps);
		values.tension = valueOrDefault$6(config.lineTension, lineOptions.tension);
		values.steppedLine = resolve$2([custom.steppedLine, config.steppedLine, lineOptions.stepped]);
		values.clip = toClip(valueOrDefault$6(config.clip, defaultClip(me._xScale, me._yScale, values.borderWidth)));

		return values;
	},

	calculatePointY: function(value, index, datasetIndex) {
		var me = this;
		var chart = me.chart;
		var yScale = me._yScale;
		var sumPos = 0;
		var sumNeg = 0;
		var i, ds, dsMeta, stackedRightValue, rightValue, metasets, ilen;

		if (yScale.options.stacked) {
			rightValue = +yScale.getRightValue(value);
			metasets = chart._getSortedVisibleDatasetMetas();
			ilen = metasets.length;

			for (i = 0; i < ilen; ++i) {
				dsMeta = metasets[i];
				if (dsMeta.index === datasetIndex) {
					break;
				}

				ds = chart.data.datasets[dsMeta.index];
				if (dsMeta.type === 'line' && dsMeta.yAxisID === yScale.id) {
					stackedRightValue = +yScale.getRightValue(ds.data[index]);
					if (stackedRightValue < 0) {
						sumNeg += stackedRightValue || 0;
					} else {
						sumPos += stackedRightValue || 0;
					}
				}
			}

			if (rightValue < 0) {
				return yScale.getPixelForValue(sumNeg + rightValue);
			}
			return yScale.getPixelForValue(sumPos + rightValue);
		}
		return yScale.getPixelForValue(value);
	},

	updateBezierControlPoints: function() {
		var me = this;
		var chart = me.chart;
		var meta = me.getMeta();
		var lineModel = meta.dataset._model;
		var area = chart.chartArea;
		var points = meta.data || [];
		var i, ilen, model, controlPoints;

		// Only consider points that are drawn in case the spanGaps option is used
		if (lineModel.spanGaps) {
			points = points.filter(function(pt) {
				return !pt._model.skip;
			});
		}

		function capControlPoint(pt, min, max) {
			return Math.max(Math.min(pt, max), min);
		}

		if (lineModel.cubicInterpolationMode === 'monotone') {
			helpers$1.splineCurveMonotone(points);
		} else {
			for (i = 0, ilen = points.length; i < ilen; ++i) {
				model = points[i]._model;
				controlPoints = helpers$1.splineCurve(
					helpers$1.previousItem(points, i)._model,
					model,
					helpers$1.nextItem(points, i)._model,
					lineModel.tension
				);
				model.controlPointPreviousX = controlPoints.previous.x;
				model.controlPointPreviousY = controlPoints.previous.y;
				model.controlPointNextX = controlPoints.next.x;
				model.controlPointNextY = controlPoints.next.y;
			}
		}

		if (chart.options.elements.line.capBezierPoints) {
			for (i = 0, ilen = points.length; i < ilen; ++i) {
				model = points[i]._model;
				if (isPointInArea(model, area)) {
					if (i > 0 && isPointInArea(points[i - 1]._model, area)) {
						model.controlPointPreviousX = capControlPoint(model.controlPointPreviousX, area.left, area.right);
						model.controlPointPreviousY = capControlPoint(model.controlPointPreviousY, area.top, area.bottom);
					}
					if (i < points.length - 1 && isPointInArea(points[i + 1]._model, area)) {
						model.controlPointNextX = capControlPoint(model.controlPointNextX, area.left, area.right);
						model.controlPointNextY = capControlPoint(model.controlPointNextY, area.top, area.bottom);
					}
				}
			}
		}
	},

	draw: function() {
		var me = this;
		var chart = me.chart;
		var meta = me.getMeta();
		var points = meta.data || [];
		var area = chart.chartArea;
		var canvas = chart.canvas;
		var i = 0;
		var ilen = points.length;
		var clip;

		if (me._showLine) {
			clip = meta.dataset._model.clip;

			helpers$1.canvas.clipArea(chart.ctx, {
				left: clip.left === false ? 0 : area.left - clip.left,
				right: clip.right === false ? canvas.width : area.right + clip.right,
				top: clip.top === false ? 0 : area.top - clip.top,
				bottom: clip.bottom === false ? canvas.height : area.bottom + clip.bottom
			});

			meta.dataset.draw();

			helpers$1.canvas.unclipArea(chart.ctx);
		}

		// Draw the points
		for (; i < ilen; ++i) {
			points[i].draw(area);
		}
	},

	/**
	 * @protected
	 */
	setHoverStyle: function(point) {
		var model = point._model;
		var options = point._options;
		var getHoverColor = helpers$1.getHoverColor;

		point.$previousStyle = {
			backgroundColor: model.backgroundColor,
			borderColor: model.borderColor,
			borderWidth: model.borderWidth,
			radius: model.radius
		};

		model.backgroundColor = valueOrDefault$6(options.hoverBackgroundColor, getHoverColor(options.backgroundColor));
		model.borderColor = valueOrDefault$6(options.hoverBorderColor, getHoverColor(options.borderColor));
		model.borderWidth = valueOrDefault$6(options.hoverBorderWidth, options.borderWidth);
		model.radius = valueOrDefault$6(options.hoverRadius, options.radius);
	},
});

var resolve$3 = helpers$1.options.resolve;

core_defaults._set('polarArea', {
	scale: {
		type: 'radialLinear',
		angleLines: {
			display: false
		},
		gridLines: {
			circular: true
		},
		pointLabels: {
			display: false
		},
		ticks: {
			beginAtZero: true
		}
	},

	// Boolean - Whether to animate the rotation of the chart
	animation: {
		animateRotate: true,
		animateScale: true
	},

	startAngle: -0.5 * Math.PI,
	legendCallback: function(chart) {
		var list = document.createElement('ul');
		var data = chart.data;
		var datasets = data.datasets;
		var labels = data.labels;
		var i, ilen, listItem, listItemSpan;

		list.setAttribute('class', chart.id + '-legend');
		if (datasets.length) {
			for (i = 0, ilen = datasets[0].data.length; i < ilen; ++i) {
				listItem = list.appendChild(document.createElement('li'));
				listItemSpan = listItem.appendChild(document.createElement('span'));
				listItemSpan.style.backgroundColor = datasets[0].backgroundColor[i];
				if (labels[i]) {
					listItem.appendChild(document.createTextNode(labels[i]));
				}
			}
		}

		return list.outerHTML;
	},
	legend: {
		labels: {
			generateLabels: function(chart) {
				var data = chart.data;
				if (data.labels.length && data.datasets.length) {
					return data.labels.map(function(label, i) {
						var meta = chart.getDatasetMeta(0);
						var style = meta.controller.getStyle(i);

						return {
							text: label,
							fillStyle: style.backgroundColor,
							strokeStyle: style.borderColor,
							lineWidth: style.borderWidth,
							hidden: isNaN(data.datasets[0].data[i]) || meta.data[i].hidden,

							// Extra data used for toggling the correct item
							index: i
						};
					});
				}
				return [];
			}
		},

		onClick: function(e, legendItem) {
			var index = legendItem.index;
			var chart = this.chart;
			var i, ilen, meta;

			for (i = 0, ilen = (chart.data.datasets || []).length; i < ilen; ++i) {
				meta = chart.getDatasetMeta(i);
				meta.data[index].hidden = !meta.data[index].hidden;
			}

			chart.update();
		}
	},

	// Need to override these to give a nice default
	tooltips: {
		callbacks: {
			title: function() {
				return '';
			},
			label: function(item, data) {
				return data.labels[item.index] + ': ' + item.yLabel;
			}
		}
	}
});

var controller_polarArea = core_datasetController.extend({

	dataElementType: elements.Arc,

	linkScales: helpers$1.noop,

	/**
	 * @private
	 */
	_dataElementOptions: [
		'backgroundColor',
		'borderColor',
		'borderWidth',
		'borderAlign',
		'hoverBackgroundColor',
		'hoverBorderColor',
		'hoverBorderWidth',
	],

	/**
	 * @private
	 */
	_getIndexScaleId: function() {
		return this.chart.scale.id;
	},

	/**
	 * @private
	 */
	_getValueScaleId: function() {
		return this.chart.scale.id;
	},

	update: function(reset) {
		var me = this;
		var dataset = me.getDataset();
		var meta = me.getMeta();
		var start = me.chart.options.startAngle || 0;
		var starts = me._starts = [];
		var angles = me._angles = [];
		var arcs = meta.data;
		var i, ilen, angle;

		me._updateRadius();

		meta.count = me.countVisibleElements();

		for (i = 0, ilen = dataset.data.length; i < ilen; i++) {
			starts[i] = start;
			angle = me._computeAngle(i);
			angles[i] = angle;
			start += angle;
		}

		for (i = 0, ilen = arcs.length; i < ilen; ++i) {
			arcs[i]._options = me._resolveDataElementOptions(arcs[i], i);
			me.updateElement(arcs[i], i, reset);
		}
	},

	/**
	 * @private
	 */
	_updateRadius: function() {
		var me = this;
		var chart = me.chart;
		var chartArea = chart.chartArea;
		var opts = chart.options;
		var minSize = Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);

		chart.outerRadius = Math.max(minSize / 2, 0);
		chart.innerRadius = Math.max(opts.cutoutPercentage ? (chart.outerRadius / 100) * (opts.cutoutPercentage) : 1, 0);
		chart.radiusLength = (chart.outerRadius - chart.innerRadius) / chart.getVisibleDatasetCount();

		me.outerRadius = chart.outerRadius - (chart.radiusLength * me.index);
		me.innerRadius = me.outerRadius - chart.radiusLength;
	},

	updateElement: function(arc, index, reset) {
		var me = this;
		var chart = me.chart;
		var dataset = me.getDataset();
		var opts = chart.options;
		var animationOpts = opts.animation;
		var scale = chart.scale;
		var labels = chart.data.labels;

		var centerX = scale.xCenter;
		var centerY = scale.yCenter;

		// var negHalfPI = -0.5 * Math.PI;
		var datasetStartAngle = opts.startAngle;
		var distance = arc.hidden ? 0 : scale.getDistanceFromCenterForValue(dataset.data[index]);
		var startAngle = me._starts[index];
		var endAngle = startAngle + (arc.hidden ? 0 : me._angles[index]);

		var resetRadius = animationOpts.animateScale ? 0 : scale.getDistanceFromCenterForValue(dataset.data[index]);
		var options = arc._options || {};

		helpers$1.extend(arc, {
			// Utility
			_datasetIndex: me.index,
			_index: index,
			_scale: scale,

			// Desired view properties
			_model: {
				backgroundColor: options.backgroundColor,
				borderColor: options.borderColor,
				borderWidth: options.borderWidth,
				borderAlign: options.borderAlign,
				x: centerX,
				y: centerY,
				innerRadius: 0,
				outerRadius: reset ? resetRadius : distance,
				startAngle: reset && animationOpts.animateRotate ? datasetStartAngle : startAngle,
				endAngle: reset && animationOpts.animateRotate ? datasetStartAngle : endAngle,
				label: helpers$1.valueAtIndexOrDefault(labels, index, labels[index])
			}
		});

		arc.pivot();
	},

	countVisibleElements: function() {
		var dataset = this.getDataset();
		var meta = this.getMeta();
		var count = 0;

		helpers$1.each(meta.data, function(element, index) {
			if (!isNaN(dataset.data[index]) && !element.hidden) {
				count++;
			}
		});

		return count;
	},

	/**
	 * @protected
	 */
	setHoverStyle: function(arc) {
		var model = arc._model;
		var options = arc._options;
		var getHoverColor = helpers$1.getHoverColor;
		var valueOrDefault = helpers$1.valueOrDefault;

		arc.$previousStyle = {
			backgroundColor: model.backgroundColor,
			borderColor: model.borderColor,
			borderWidth: model.borderWidth,
		};

		model.backgroundColor = valueOrDefault(options.hoverBackgroundColor, getHoverColor(options.backgroundColor));
		model.borderColor = valueOrDefault(options.hoverBorderColor, getHoverColor(options.borderColor));
		model.borderWidth = valueOrDefault(options.hoverBorderWidth, options.borderWidth);
	},

	/**
	 * @private
	 */
	_computeAngle: function(index) {
		var me = this;
		var count = this.getMeta().count;
		var dataset = me.getDataset();
		var meta = me.getMeta();

		if (isNaN(dataset.data[index]) || meta.data[index].hidden) {
			return 0;
		}

		// Scriptable options
		var context = {
			chart: me.chart,
			dataIndex: index,
			dataset: dataset,
			datasetIndex: me.index
		};

		return resolve$3([
			me.chart.options.elements.arc.angle,
			(2 * Math.PI) / count
		], context, index);
	}
});

core_defaults._set('pie', helpers$1.clone(core_defaults.doughnut));
core_defaults._set('pie', {
	cutoutPercentage: 0
});

// Pie charts are Doughnut chart with different defaults
var controller_pie = controller_doughnut;

var valueOrDefault$7 = helpers$1.valueOrDefault;

core_defaults._set('radar', {
	spanGaps: false,
	scale: {
		type: 'radialLinear'
	},
	elements: {
		line: {
			fill: 'start',
			tension: 0 // no bezier in radar
		}
	}
});

var controller_radar = core_datasetController.extend({
	datasetElementType: elements.Line,

	dataElementType: elements.Point,

	linkScales: helpers$1.noop,

	/**
	 * @private
	 */
	_datasetElementOptions: [
		'backgroundColor',
		'borderWidth',
		'borderColor',
		'borderCapStyle',
		'borderDash',
		'borderDashOffset',
		'borderJoinStyle',
		'fill'
	],

	/**
	 * @private
	 */
	_dataElementOptions: {
		backgroundColor: 'pointBackgroundColor',
		borderColor: 'pointBorderColor',
		borderWidth: 'pointBorderWidth',
		hitRadius: 'pointHitRadius',
		hoverBackgroundColor: 'pointHoverBackgroundColor',
		hoverBorderColor: 'pointHoverBorderColor',
		hoverBorderWidth: 'pointHoverBorderWidth',
		hoverRadius: 'pointHoverRadius',
		pointStyle: 'pointStyle',
		radius: 'pointRadius',
		rotation: 'pointRotation'
	},

	/**
	 * @private
	 */
	_getIndexScaleId: function() {
		return this.chart.scale.id;
	},

	/**
	 * @private
	 */
	_getValueScaleId: function() {
		return this.chart.scale.id;
	},

	update: function(reset) {
		var me = this;
		var meta = me.getMeta();
		var line = meta.dataset;
		var points = meta.data || [];
		var scale = me.chart.scale;
		var config = me._config;
		var i, ilen;

		// Compatibility: If the properties are defined with only the old name, use those values
		if (config.tension !== undefined && config.lineTension === undefined) {
			config.lineTension = config.tension;
		}

		// Utility
		line._scale = scale;
		line._datasetIndex = me.index;
		// Data
		line._children = points;
		line._loop = true;
		// Model
		line._model = me._resolveDatasetElementOptions(line);

		line.pivot();

		// Update Points
		for (i = 0, ilen = points.length; i < ilen; ++i) {
			me.updateElement(points[i], i, reset);
		}

		// Update bezier control points
		me.updateBezierControlPoints();

		// Now pivot the point for animation
		for (i = 0, ilen = points.length; i < ilen; ++i) {
			points[i].pivot();
		}
	},

	updateElement: function(point, index, reset) {
		var me = this;
		var custom = point.custom || {};
		var dataset = me.getDataset();
		var scale = me.chart.scale;
		var pointPosition = scale.getPointPositionForValue(index, dataset.data[index]);
		var options = me._resolveDataElementOptions(point, index);
		var lineModel = me.getMeta().dataset._model;
		var x = reset ? scale.xCenter : pointPosition.x;
		var y = reset ? scale.yCenter : pointPosition.y;

		// Utility
		point._scale = scale;
		point._options = options;
		point._datasetIndex = me.index;
		point._index = index;

		// Desired view properties
		point._model = {
			x: x, // value not used in dataset scale, but we want a consistent API between scales
			y: y,
			skip: custom.skip || isNaN(x) || isNaN(y),
			// Appearance
			radius: options.radius,
			pointStyle: options.pointStyle,
			rotation: options.rotation,
			backgroundColor: options.backgroundColor,
			borderColor: options.borderColor,
			borderWidth: options.borderWidth,
			tension: valueOrDefault$7(custom.tension, lineModel ? lineModel.tension : 0),

			// Tooltip
			hitRadius: options.hitRadius
		};
	},

	/**
	 * @private
	 */
	_resolveDatasetElementOptions: function() {
		var me = this;
		var config = me._config;
		var options = me.chart.options;
		var values = core_datasetController.prototype._resolveDatasetElementOptions.apply(me, arguments);

		values.spanGaps = valueOrDefault$7(config.spanGaps, options.spanGaps);
		values.tension = valueOrDefault$7(config.lineTension, options.elements.line.tension);

		return values;
	},

	updateBezierControlPoints: function() {
		var me = this;
		var meta = me.getMeta();
		var area = me.chart.chartArea;
		var points = meta.data || [];
		var i, ilen, model, controlPoints;

		// Only consider points that are drawn in case the spanGaps option is used
		if (meta.dataset._model.spanGaps) {
			points = points.filter(function(pt) {
				return !pt._model.skip;
			});
		}

		function capControlPoint(pt, min, max) {
			return Math.max(Math.min(pt, max), min);
		}

		for (i = 0, ilen = points.length; i < ilen; ++i) {
			model = points[i]._model;
			controlPoints = helpers$1.splineCurve(
				helpers$1.previousItem(points, i, true)._model,
				model,
				helpers$1.nextItem(points, i, true)._model,
				model.tension
			);

			// Prevent the bezier going outside of the bounds of the graph
			model.controlPointPreviousX = capControlPoint(controlPoints.previous.x, area.left, area.right);
			model.controlPointPreviousY = capControlPoint(controlPoints.previous.y, area.top, area.bottom);
			model.controlPointNextX = capControlPoint(controlPoints.next.x, area.left, area.right);
			model.controlPointNextY = capControlPoint(controlPoints.next.y, area.top, area.bottom);
		}
	},

	setHoverStyle: function(point) {
		var model = point._model;
		var options = point._options;
		var getHoverColor = helpers$1.getHoverColor;

		point.$previousStyle = {
			backgroundColor: model.backgroundColor,
			borderColor: model.borderColor,
			borderWidth: model.borderWidth,
			radius: model.radius
		};

		model.backgroundColor = valueOrDefault$7(options.hoverBackgroundColor, getHoverColor(options.backgroundColor));
		model.borderColor = valueOrDefault$7(options.hoverBorderColor, getHoverColor(options.borderColor));
		model.borderWidth = valueOrDefault$7(options.hoverBorderWidth, options.borderWidth);
		model.radius = valueOrDefault$7(options.hoverRadius, options.radius);
	}
});

core_defaults._set('scatter', {
	hover: {
		mode: 'single'
	},

	scales: {
		xAxes: [{
			id: 'x-axis-1',    // need an ID so datasets can reference the scale
			type: 'linear',    // scatter should not use a category axis
			position: 'bottom'
		}],
		yAxes: [{
			id: 'y-axis-1',
			type: 'linear',
			position: 'left'
		}]
	},

	tooltips: {
		callbacks: {
			title: function() {
				return '';     // doesn't make sense for scatter since data are formatted as a point
			},
			label: function(item) {
				return '(' + item.xLabel + ', ' + item.yLabel + ')';
			}
		}
	}
});

core_defaults._set('global', {
	datasets: {
		scatter: {
			showLine: false
		}
	}
});

// Scatter charts use line controllers
var controller_scatter = controller_line;

// NOTE export a map in which the key represents the controller type, not
// the class, and so must be CamelCase in order to be correctly retrieved
// by the controller in core.controller.js (`controllers[meta.type]`).

var controllers = {
	bar: controller_bar,
	bubble: controller_bubble,
	doughnut: controller_doughnut,
	horizontalBar: controller_horizontalBar,
	line: controller_line,
	polarArea: controller_polarArea,
	pie: controller_pie,
	radar: controller_radar,
	scatter: controller_scatter
};

/**
 * Helper function to get relative position for an event
 * @param {Event|IEvent} event - The event to get the position for
 * @param {Chart} chart - The chart
 * @returns {object} the event position
 */
function getRelativePosition(e, chart) {
	if (e.native) {
		return {
			x: e.x,
			y: e.y
		};
	}

	return helpers$1.getRelativePosition(e, chart);
}

/**
 * Helper function to traverse all of the visible elements in the chart
 * @param {Chart} chart - the chart
 * @param {function} handler - the callback to execute for each visible item
 */
function parseVisibleItems(chart, handler) {
	var metasets = chart._getSortedVisibleDatasetMetas();
	var metadata, i, j, ilen, jlen, element;

	for (i = 0, ilen = metasets.length; i < ilen; ++i) {
		metadata = metasets[i].data;
		for (j = 0, jlen = metadata.length; j < jlen; ++j) {
			element = metadata[j];
			if (!element._view.skip) {
				handler(element);
			}
		}
	}
}

/**
 * Helper function to get the items that intersect the event position
 * @param {ChartElement[]} items - elements to filter
 * @param {object} position - the point to be nearest to
 * @return {ChartElement[]} the nearest items
 */
function getIntersectItems(chart, position) {
	var elements = [];

	parseVisibleItems(chart, function(element) {
		if (element.inRange(position.x, position.y)) {
			elements.push(element);
		}
	});

	return elements;
}

/**
 * Helper function to get the items nearest to the event position considering all visible items in teh chart
 * @param {Chart} chart - the chart to look at elements from
 * @param {object} position - the point to be nearest to
 * @param {boolean} intersect - if true, only consider items that intersect the position
 * @param {function} distanceMetric - function to provide the distance between points
 * @return {ChartElement[]} the nearest items
 */
function getNearestItems(chart, position, intersect, distanceMetric) {
	var minDistance = Number.POSITIVE_INFINITY;
	var nearestItems = [];

	parseVisibleItems(chart, function(element) {
		if (intersect && !element.inRange(position.x, position.y)) {
			return;
		}

		var center = element.getCenterPoint();
		var distance = distanceMetric(position, center);
		if (distance < minDistance) {
			nearestItems = [element];
			minDistance = distance;
		} else if (distance === minDistance) {
			// Can have multiple items at the same distance in which case we sort by size
			nearestItems.push(element);
		}
	});

	return nearestItems;
}

/**
 * Get a distance metric function for two points based on the
 * axis mode setting
 * @param {string} axis - the axis mode. x|y|xy
 */
function getDistanceMetricForAxis(axis) {
	var useX = axis.indexOf('x') !== -1;
	var useY = axis.indexOf('y') !== -1;

	return function(pt1, pt2) {
		var deltaX = useX ? Math.abs(pt1.x - pt2.x) : 0;
		var deltaY = useY ? Math.abs(pt1.y - pt2.y) : 0;
		return Math.sqrt(Math.pow(deltaX, 2) + Math.pow(deltaY, 2));
	};
}

function indexMode(chart, e, options) {
	var position = getRelativePosition(e, chart);
	// Default axis for index mode is 'x' to match old behaviour
	options.axis = options.axis || 'x';
	var distanceMetric = getDistanceMetricForAxis(options.axis);
	var items = options.intersect ? getIntersectItems(chart, position) : getNearestItems(chart, position, false, distanceMetric);
	var elements = [];

	if (!items.length) {
		return [];
	}

	chart._getSortedVisibleDatasetMetas().forEach(function(meta) {
		var element = meta.data[items[0]._index];

		// don't count items that are skipped (null data)
		if (element && !element._view.skip) {
			elements.push(element);
		}
	});

	return elements;
}

/**
 * @interface IInteractionOptions
 */
/**
 * If true, only consider items that intersect the point
 * @name IInterfaceOptions#boolean
 * @type Boolean
 */

/**
 * Contains interaction related functions
 * @namespace Chart.Interaction
 */
var core_interaction = {
	// Helper function for different modes
	modes: {
		single: function(chart, e) {
			var position = getRelativePosition(e, chart);
			var elements = [];

			parseVisibleItems(chart, function(element) {
				if (element.inRange(position.x, position.y)) {
					elements.push(element);
					return elements;
				}
			});

			return elements.slice(0, 1);
		},

		/**
		 * @function Chart.Interaction.modes.label
		 * @deprecated since version 2.4.0
		 * @todo remove at version 3
		 * @private
		 */
		label: indexMode,

		/**
		 * Returns items at the same index. If the options.intersect parameter is true, we only return items if we intersect something
		 * If the options.intersect mode is false, we find the nearest item and return the items at the same index as that item
		 * @function Chart.Interaction.modes.index
		 * @since v2.4.0
		 * @param {Chart} chart - the chart we are returning items from
		 * @param {Event} e - the event we are find things at
		 * @param {IInteractionOptions} options - options to use during interaction
		 * @return {Chart.Element[]} Array of elements that are under the point. If none are found, an empty array is returned
		 */
		index: indexMode,

		/**
		 * Returns items in the same dataset. If the options.intersect parameter is true, we only return items if we intersect something
		 * If the options.intersect is false, we find the nearest item and return the items in that dataset
		 * @function Chart.Interaction.modes.dataset
		 * @param {Chart} chart - the chart we are returning items from
		 * @param {Event} e - the event we are find things at
		 * @param {IInteractionOptions} options - options to use during interaction
		 * @return {Chart.Element[]} Array of elements that are under the point. If none are found, an empty array is returned
		 */
		dataset: function(chart, e, options) {
			var position = getRelativePosition(e, chart);
			options.axis = options.axis || 'xy';
			var distanceMetric = getDistanceMetricForAxis(options.axis);
			var items = options.intersect ? getIntersectItems(chart, position) : getNearestItems(chart, position, false, distanceMetric);

			if (items.length > 0) {
				items = chart.getDatasetMeta(items[0]._datasetIndex).data;
			}

			return items;
		},

		/**
		 * @function Chart.Interaction.modes.x-axis
		 * @deprecated since version 2.4.0. Use index mode and intersect == true
		 * @todo remove at version 3
		 * @private
		 */
		'x-axis': function(chart, e) {
			return indexMode(chart, e, {intersect: false});
		},

		/**
		 * Point mode returns all elements that hit test based on the event position
		 * of the event
		 * @function Chart.Interaction.modes.intersect
		 * @param {Chart} chart - the chart we are returning items from
		 * @param {Event} e - the event we are find things at
		 * @return {Chart.Element[]} Array of elements that are under the point. If none are found, an empty array is returned
		 */
		point: function(chart, e) {
			var position = getRelativePosition(e, chart);
			return getIntersectItems(chart, position);
		},

		/**
		 * nearest mode returns the element closest to the point
		 * @function Chart.Interaction.modes.intersect
		 * @param {Chart} chart - the chart we are returning items from
		 * @param {Event} e - the event we are find things at
		 * @param {IInteractionOptions} options - options to use
		 * @return {Chart.Element[]} Array of elements that are under the point. If none are found, an empty array is returned
		 */
		nearest: function(chart, e, options) {
			var position = getRelativePosition(e, chart);
			options.axis = options.axis || 'xy';
			var distanceMetric = getDistanceMetricForAxis(options.axis);
			return getNearestItems(chart, position, options.intersect, distanceMetric);
		},

		/**
		 * x mode returns the elements that hit-test at the current x coordinate
		 * @function Chart.Interaction.modes.x
		 * @param {Chart} chart - the chart we are returning items from
		 * @param {Event} e - the event we are find things at
		 * @param {IInteractionOptions} options - options to use
		 * @return {Chart.Element[]} Array of elements that are under the point. If none are found, an empty array is returned
		 */
		x: function(chart, e, options) {
			var position = getRelativePosition(e, chart);
			var items = [];
			var intersectsItem = false;

			parseVisibleItems(chart, function(element) {
				if (element.inXRange(position.x)) {
					items.push(element);
				}

				if (element.inRange(position.x, position.y)) {
					intersectsItem = true;
				}
			});

			// If we want to trigger on an intersect and we don't have any items
			// that intersect the position, return nothing
			if (options.intersect && !intersectsItem) {
				items = [];
			}
			return items;
		},

		/**
		 * y mode returns the elements that hit-test at the current y coordinate
		 * @function Chart.Interaction.modes.y
		 * @param {Chart} chart - the chart we are returning items from
		 * @param {Event} e - the event we are find things at
		 * @param {IInteractionOptions} options - options to use
		 * @return {Chart.Element[]} Array of elements that are under the point. If none are found, an empty array is returned
		 */
		y: function(chart, e, options) {
			var position = getRelativePosition(e, chart);
			var items = [];
			var intersectsItem = false;

			parseVisibleItems(chart, function(element) {
				if (element.inYRange(position.y)) {
					items.push(element);
				}

				if (element.inRange(position.x, position.y)) {
					intersectsItem = true;
				}
			});

			// If we want to trigger on an intersect and we don't have any items
			// that intersect the position, return nothing
			if (options.intersect && !intersectsItem) {
				items = [];
			}
			return items;
		}
	}
};

var extend = helpers$1.extend;

function filterByPosition(array, position) {
	return helpers$1.where(array, function(v) {
		return v.pos === position;
	});
}

function sortByWeight(array, reverse) {
	return array.sort(function(a, b) {
		var v0 = reverse ? b : a;
		var v1 = reverse ? a : b;
		return v0.weight === v1.weight ?
			v0.index - v1.index :
			v0.weight - v1.weight;
	});
}

function wrapBoxes(boxes) {
	var layoutBoxes = [];
	var i, ilen, box;

	for (i = 0, ilen = (boxes || []).length; i < ilen; ++i) {
		box = boxes[i];
		layoutBoxes.push({
			index: i,
			box: box,
			pos: box.position,
			horizontal: box.isHorizontal(),
			weight: box.weight
		});
	}
	return layoutBoxes;
}

function setLayoutDims(layouts, params) {
	var i, ilen, layout;
	for (i = 0, ilen = layouts.length; i < ilen; ++i) {
		layout = layouts[i];
		// store width used instead of chartArea.w in fitBoxes
		layout.width = layout.horizontal
			? layout.box.fullWidth && params.availableWidth
			: params.vBoxMaxWidth;
		// store height used instead of chartArea.h in fitBoxes
		layout.height = layout.horizontal && params.hBoxMaxHeight;
	}
}

function buildLayoutBoxes(boxes) {
	var layoutBoxes = wrapBoxes(boxes);
	var left = sortByWeight(filterByPosition(layoutBoxes, 'left'), true);
	var right = sortByWeight(filterByPosition(layoutBoxes, 'right'));
	var top = sortByWeight(filterByPosition(layoutBoxes, 'top'), true);
	var bottom = sortByWeight(filterByPosition(layoutBoxes, 'bottom'));

	return {
		leftAndTop: left.concat(top),
		rightAndBottom: right.concat(bottom),
		chartArea: filterByPosition(layoutBoxes, 'chartArea'),
		vertical: left.concat(right),
		horizontal: top.concat(bottom)
	};
}

function getCombinedMax(maxPadding, chartArea, a, b) {
	return Math.max(maxPadding[a], chartArea[a]) + Math.max(maxPadding[b], chartArea[b]);
}

function updateDims(chartArea, params, layout) {
	var box = layout.box;
	var maxPadding = chartArea.maxPadding;
	var newWidth, newHeight;

	if (layout.size) {
		// this layout was already counted for, lets first reduce old size
		chartArea[layout.pos] -= layout.size;
	}
	layout.size = layout.horizontal ? box.height : box.width;
	chartArea[layout.pos] += layout.size;

	if (box.getPadding) {
		var boxPadding = box.getPadding();
		maxPadding.top = Math.max(maxPadding.top, boxPadding.top);
		maxPadding.left = Math.max(maxPadding.left, boxPadding.left);
		maxPadding.bottom = Math.max(maxPadding.bottom, boxPadding.bottom);
		maxPadding.right = Math.max(maxPadding.right, boxPadding.right);
	}

	newWidth = params.outerWidth - getCombinedMax(maxPadding, chartArea, 'left', 'right');
	newHeight = params.outerHeight - getCombinedMax(maxPadding, chartArea, 'top', 'bottom');

	if (newWidth !== chartArea.w || newHeight !== chartArea.h) {
		chartArea.w = newWidth;
		chartArea.h = newHeight;

		// return true if chart area changed in layout's direction
		var sizes = layout.horizontal ? [newWidth, chartArea.w] : [newHeight, chartArea.h];
		return sizes[0] !== sizes[1] && (!isNaN(sizes[0]) || !isNaN(sizes[1]));
	}
}

function handleMaxPadding(chartArea) {
	var maxPadding = chartArea.maxPadding;

	function updatePos(pos) {
		var change = Math.max(maxPadding[pos] - chartArea[pos], 0);
		chartArea[pos] += change;
		return change;
	}
	chartArea.y += updatePos('top');
	chartArea.x += updatePos('left');
	updatePos('right');
	updatePos('bottom');
}

function getMargins(horizontal, chartArea) {
	var maxPadding = chartArea.maxPadding;

	function marginForPositions(positions) {
		var margin = {left: 0, top: 0, right: 0, bottom: 0};
		positions.forEach(function(pos) {
			margin[pos] = Math.max(chartArea[pos], maxPadding[pos]);
		});
		return margin;
	}

	return horizontal
		? marginForPositions(['left', 'right'])
		: marginForPositions(['top', 'bottom']);
}

function fitBoxes(boxes, chartArea, params) {
	var refitBoxes = [];
	var i, ilen, layout, box, refit, changed;

	for (i = 0, ilen = boxes.length; i < ilen; ++i) {
		layout = boxes[i];
		box = layout.box;

		box.update(
			layout.width || chartArea.w,
			layout.height || chartArea.h,
			getMargins(layout.horizontal, chartArea)
		);
		if (updateDims(chartArea, params, layout)) {
			changed = true;
			if (refitBoxes.length) {
				// Dimensions changed and there were non full width boxes before this
				// -> we have to refit those
				refit = true;
			}
		}
		if (!box.fullWidth) { // fullWidth boxes don't need to be re-fitted in any case
			refitBoxes.push(layout);
		}
	}

	return refit ? fitBoxes(refitBoxes, chartArea, params) || changed : changed;
}

function placeBoxes(boxes, chartArea, params) {
	var userPadding = params.padding;
	var x = chartArea.x;
	var y = chartArea.y;
	var i, ilen, layout, box;

	for (i = 0, ilen = boxes.length; i < ilen; ++i) {
		layout = boxes[i];
		box = layout.box;
		if (layout.horizontal) {
			box.left = box.fullWidth ? userPadding.left : chartArea.left;
			box.right = box.fullWidth ? params.outerWidth - userPadding.right : chartArea.left + chartArea.w;
			box.top = y;
			box.bottom = y + box.height;
			box.width = box.right - box.left;
			y = box.bottom;
		} else {
			box.left = x;
			box.right = x + box.width;
			box.top = chartArea.top;
			box.bottom = chartArea.top + chartArea.h;
			box.height = box.bottom - box.top;
			x = box.right;
		}
	}

	chartArea.x = x;
	chartArea.y = y;
}

core_defaults._set('global', {
	layout: {
		padding: {
			top: 0,
			right: 0,
			bottom: 0,
			left: 0
		}
	}
});

/**
 * @interface ILayoutItem
 * @prop {string} position - The position of the item in the chart layout. Possible values are
 * 'left', 'top', 'right', 'bottom', and 'chartArea'
 * @prop {number} weight - The weight used to sort the item. Higher weights are further away from the chart area
 * @prop {boolean} fullWidth - if true, and the item is horizontal, then push vertical boxes down
 * @prop {function} isHorizontal - returns true if the layout item is horizontal (ie. top or bottom)
 * @prop {function} update - Takes two parameters: width and height. Returns size of item
 * @prop {function} getPadding -  Returns an object with padding on the edges
 * @prop {number} width - Width of item. Must be valid after update()
 * @prop {number} height - Height of item. Must be valid after update()
 * @prop {number} left - Left edge of the item. Set by layout system and cannot be used in update
 * @prop {number} top - Top edge of the item. Set by layout system and cannot be used in update
 * @prop {number} right - Right edge of the item. Set by layout system and cannot be used in update
 * @prop {number} bottom - Bottom edge of the item. Set by layout system and cannot be used in update
 */

// The layout service is very self explanatory.  It's responsible for the layout within a chart.
// Scales, Legends and Plugins all rely on the layout service and can easily register to be placed anywhere they need
// It is this service's responsibility of carrying out that layout.
var core_layouts = {
	defaults: {},

	/**
	 * Register a box to a chart.
	 * A box is simply a reference to an object that requires layout. eg. Scales, Legend, Title.
	 * @param {Chart} chart - the chart to use
	 * @param {ILayoutItem} item - the item to add to be layed out
	 */
	addBox: function(chart, item) {
		if (!chart.boxes) {
			chart.boxes = [];
		}

		// initialize item with default values
		item.fullWidth = item.fullWidth || false;
		item.position = item.position || 'top';
		item.weight = item.weight || 0;
		item._layers = item._layers || function() {
			return [{
				z: 0,
				draw: function() {
					item.draw.apply(item, arguments);
				}
			}];
		};

		chart.boxes.push(item);
	},

	/**
	 * Remove a layoutItem from a chart
	 * @param {Chart} chart - the chart to remove the box from
	 * @param {ILayoutItem} layoutItem - the item to remove from the layout
	 */
	removeBox: function(chart, layoutItem) {
		var index = chart.boxes ? chart.boxes.indexOf(layoutItem) : -1;
		if (index !== -1) {
			chart.boxes.splice(index, 1);
		}
	},

	/**
	 * Sets (or updates) options on the given `item`.
	 * @param {Chart} chart - the chart in which the item lives (or will be added to)
	 * @param {ILayoutItem} item - the item to configure with the given options
	 * @param {object} options - the new item options.
	 */
	configure: function(chart, item, options) {
		var props = ['fullWidth', 'position', 'weight'];
		var ilen = props.length;
		var i = 0;
		var prop;

		for (; i < ilen; ++i) {
			prop = props[i];
			if (options.hasOwnProperty(prop)) {
				item[prop] = options[prop];
			}
		}
	},

	/**
	 * Fits boxes of the given chart into the given size by having each box measure itself
	 * then running a fitting algorithm
	 * @param {Chart} chart - the chart
	 * @param {number} width - the width to fit into
	 * @param {number} height - the height to fit into
	 */
	update: function(chart, width, height) {
		if (!chart) {
			return;
		}

		var layoutOptions = chart.options.layout || {};
		var padding = helpers$1.options.toPadding(layoutOptions.padding);

		var availableWidth = width - padding.width;
		var availableHeight = height - padding.height;
		var boxes = buildLayoutBoxes(chart.boxes);
		var verticalBoxes = boxes.vertical;
		var horizontalBoxes = boxes.horizontal;

		// Essentially we now have any number of boxes on each of the 4 sides.
		// Our canvas looks like the following.
		// The areas L1 and L2 are the left axes. R1 is the right axis, T1 is the top axis and
		// B1 is the bottom axis
		// There are also 4 quadrant-like locations (left to right instead of clockwise) reserved for chart overlays
		// These locations are single-box locations only, when trying to register a chartArea location that is already taken,
		// an error will be thrown.
		//
		// |----------------------------------------------------|
		// |                  T1 (Full Width)                   |
		// |----------------------------------------------------|
		// |    |    |                 T2                  |    |
		// |    |----|-------------------------------------|----|
		// |    |    | C1 |                           | C2 |    |
		// |    |    |----|                           |----|    |
		// |    |    |                                     |    |
		// | L1 | L2 |           ChartArea (C0)            | R1 |
		// |    |    |                                     |    |
		// |    |    |----|                           |----|    |
		// |    |    | C3 |                           | C4 |    |
		// |    |----|-------------------------------------|----|
		// |    |    |                 B1                  |    |
		// |----------------------------------------------------|
		// |                  B2 (Full Width)                   |
		// |----------------------------------------------------|
		//

		var params = Object.freeze({
			outerWidth: width,
			outerHeight: height,
			padding: padding,
			availableWidth: availableWidth,
			vBoxMaxWidth: availableWidth / 2 / verticalBoxes.length,
			hBoxMaxHeight: availableHeight / 2
		});
		var chartArea = extend({
			maxPadding: extend({}, padding),
			w: availableWidth,
			h: availableHeight,
			x: padding.left,
			y: padding.top
		}, padding);

		setLayoutDims(verticalBoxes.concat(horizontalBoxes), params);

		// First fit vertical boxes
		fitBoxes(verticalBoxes, chartArea, params);

		// Then fit horizontal boxes
		if (fitBoxes(horizontalBoxes, chartArea, params)) {
			// if the area changed, re-fit vertical boxes
			fitBoxes(verticalBoxes, chartArea, params);
		}

		handleMaxPadding(chartArea);

		// Finally place the boxes to correct coordinates
		placeBoxes(boxes.leftAndTop, chartArea, params);

		// Move to opposite side of chart
		chartArea.x += chartArea.w;
		chartArea.y += chartArea.h;

		placeBoxes(boxes.rightAndBottom, chartArea, params);

		chart.chartArea = {
			left: chartArea.left,
			top: chartArea.top,
			right: chartArea.left + chartArea.w,
			bottom: chartArea.top + chartArea.h
		};

		// Finally update boxes in chartArea (radial scale for example)
		helpers$1.each(boxes.chartArea, function(layout) {
			var box = layout.box;
			extend(box, chart.chartArea);
			box.update(chartArea.w, chartArea.h);
		});
	}
};

/**
 * Platform fallback implementation (minimal).
 * @see https://github.com/chartjs/Chart.js/pull/4591#issuecomment-319575939
 */

var platform_basic = {
	acquireContext: function(item) {
		if (item && item.canvas) {
			// Support for any object associated to a canvas (including a context2d)
			item = item.canvas;
		}

		return item && item.getContext('2d') || null;
	}
};

var platform_dom = "/*\n * DOM element rendering detection\n * https://davidwalsh.name/detect-node-insertion\n */\n@keyframes chartjs-render-animation {\n\tfrom { opacity: 0.99; }\n\tto { opacity: 1; }\n}\n\n.chartjs-render-monitor {\n\tanimation: chartjs-render-animation 0.001s;\n}\n\n/*\n * DOM element resizing detection\n * https://github.com/marcj/css-element-queries\n */\n.chartjs-size-monitor,\n.chartjs-size-monitor-expand,\n.chartjs-size-monitor-shrink {\n\tposition: absolute;\n\tdirection: ltr;\n\tleft: 0;\n\ttop: 0;\n\tright: 0;\n\tbottom: 0;\n\toverflow: hidden;\n\tpointer-events: none;\n\tvisibility: hidden;\n\tz-index: -1;\n}\n\n.chartjs-size-monitor-expand > div {\n\tposition: absolute;\n\twidth: 1000000px;\n\theight: 1000000px;\n\tleft: 0;\n\ttop: 0;\n}\n\n.chartjs-size-monitor-shrink > div {\n\tposition: absolute;\n\twidth: 200%;\n\theight: 200%;\n\tleft: 0;\n\ttop: 0;\n}\n";

var platform_dom$1 = /*#__PURE__*/Object.freeze({
__proto__: null,
'default': platform_dom
});

var stylesheet = getCjsExportFromNamespace(platform_dom$1);

var EXPANDO_KEY = '$chartjs';
var CSS_PREFIX = 'chartjs-';
var CSS_SIZE_MONITOR = CSS_PREFIX + 'size-monitor';
var CSS_RENDER_MONITOR = CSS_PREFIX + 'render-monitor';
var CSS_RENDER_ANIMATION = CSS_PREFIX + 'render-animation';
var ANIMATION_START_EVENTS = ['animationstart', 'webkitAnimationStart'];

/**
 * DOM event types -> Chart.js event types.
 * Note: only events with different types are mapped.
 * @see https://developer.mozilla.org/en-US/docs/Web/Events
 */
var EVENT_TYPES = {
	touchstart: 'mousedown',
	touchmove: 'mousemove',
	touchend: 'mouseup',
	pointerenter: 'mouseenter',
	pointerdown: 'mousedown',
	pointermove: 'mousemove',
	pointerup: 'mouseup',
	pointerleave: 'mouseout',
	pointerout: 'mouseout'
};

/**
 * The "used" size is the final value of a dimension property after all calculations have
 * been performed. This method uses the computed style of `element` but returns undefined
 * if the computed style is not expressed in pixels. That can happen in some cases where
 * `element` has a size relative to its parent and this last one is not yet displayed,
 * for example because of `display: none` on a parent node.
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/used_value
 * @returns {number} Size in pixels or undefined if unknown.
 */
function readUsedSize(element, property) {
	var value = helpers$1.getStyle(element, property);
	var matches = value && value.match(/^(\d+)(\.\d+)?px$/);
	return matches ? Number(matches[1]) : undefined;
}

/**
 * Initializes the canvas style and render size without modifying the canvas display size,
 * since responsiveness is handled by the controller.resize() method. The config is used
 * to determine the aspect ratio to apply in case no explicit height has been specified.
 */
function initCanvas(canvas, config) {
	var style = canvas.style;

	// NOTE(SB) canvas.getAttribute('width') !== canvas.width: in the first case it
	// returns null or '' if no explicit value has been set to the canvas attribute.
	var renderHeight = canvas.getAttribute('height');
	var renderWidth = canvas.getAttribute('width');

	// Chart.js modifies some canvas values that we want to restore on destroy
	canvas[EXPANDO_KEY] = {
		initial: {
			height: renderHeight,
			width: renderWidth,
			style: {
				display: style.display,
				height: style.height,
				width: style.width
			}
		}
	};

	// Force canvas to display as block to avoid extra space caused by inline
	// elements, which would interfere with the responsive resize process.
	// https://github.com/chartjs/Chart.js/issues/2538
	style.display = style.display || 'block';

	if (renderWidth === null || renderWidth === '') {
		var displayWidth = readUsedSize(canvas, 'width');
		if (displayWidth !== undefined) {
			canvas.width = displayWidth;
		}
	}

	if (renderHeight === null || renderHeight === '') {
		if (canvas.style.height === '') {
			// If no explicit render height and style height, let's apply the aspect ratio,
			// which one can be specified by the user but also by charts as default option
			// (i.e. options.aspectRatio). If not specified, use canvas aspect ratio of 2.
			canvas.height = canvas.width / (config.options.aspectRatio || 2);
		} else {
			var displayHeight = readUsedSize(canvas, 'height');
			if (displayWidth !== undefined) {
				canvas.height = displayHeight;
			}
		}
	}

	return canvas;
}

/**
 * Detects support for options object argument in addEventListener.
 * https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Safely_detecting_option_support
 * @private
 */
var supportsEventListenerOptions = (function() {
	var supports = false;
	try {
		var options = Object.defineProperty({}, 'passive', {
			// eslint-disable-next-line getter-return
			get: function() {
				supports = true;
			}
		});
		window.addEventListener('e', null, options);
	} catch (e) {
		// continue regardless of error
	}
	return supports;
}());

// Default passive to true as expected by Chrome for 'touchstart' and 'touchend' events.
// https://github.com/chartjs/Chart.js/issues/4287
var eventListenerOptions = supportsEventListenerOptions ? {passive: true} : false;

function addListener(node, type, listener) {
	node.addEventListener(type, listener, eventListenerOptions);
}

function removeListener(node, type, listener) {
	node.removeEventListener(type, listener, eventListenerOptions);
}

function createEvent(type, chart, x, y, nativeEvent) {
	return {
		type: type,
		chart: chart,
		native: nativeEvent || null,
		x: x !== undefined ? x : null,
		y: y !== undefined ? y : null,
	};
}

function fromNativeEvent(event, chart) {
	var type = EVENT_TYPES[event.type] || event.type;
	var pos = helpers$1.getRelativePosition(event, chart);
	return createEvent(type, chart, pos.x, pos.y, event);
}

function throttled(fn, thisArg) {
	var ticking = false;
	var args = [];

	return function() {
		args = Array.prototype.slice.call(arguments);
		thisArg = thisArg || this;

		if (!ticking) {
			ticking = true;
			helpers$1.requestAnimFrame.call(window, function() {
				ticking = false;
				fn.apply(thisArg, args);
			});
		}
	};
}

function createDiv(cls) {
	var el = document.createElement('div');
	el.className = cls || '';
	return el;
}

// Implementation based on https://github.com/marcj/css-element-queries
function createResizer(handler) {
	var maxSize = 1000000;

	// NOTE(SB) Don't use innerHTML because it could be considered unsafe.
	// https://github.com/chartjs/Chart.js/issues/5902
	var resizer = createDiv(CSS_SIZE_MONITOR);
	var expand = createDiv(CSS_SIZE_MONITOR + '-expand');
	var shrink = createDiv(CSS_SIZE_MONITOR + '-shrink');

	expand.appendChild(createDiv());
	shrink.appendChild(createDiv());

	resizer.appendChild(expand);
	resizer.appendChild(shrink);
	resizer._reset = function() {
		expand.scrollLeft = maxSize;
		expand.scrollTop = maxSize;
		shrink.scrollLeft = maxSize;
		shrink.scrollTop = maxSize;
	};

	var onScroll = function() {
		resizer._reset();
		handler();
	};

	addListener(expand, 'scroll', onScroll.bind(expand, 'expand'));
	addListener(shrink, 'scroll', onScroll.bind(shrink, 'shrink'));

	return resizer;
}

// https://davidwalsh.name/detect-node-insertion
function watchForRender(node, handler) {
	var expando = node[EXPANDO_KEY] || (node[EXPANDO_KEY] = {});
	var proxy = expando.renderProxy = function(e) {
		if (e.animationName === CSS_RENDER_ANIMATION) {
			handler();
		}
	};

	helpers$1.each(ANIMATION_START_EVENTS, function(type) {
		addListener(node, type, proxy);
	});

	// #4737: Chrome might skip the CSS animation when the CSS_RENDER_MONITOR class
	// is removed then added back immediately (same animation frame?). Accessing the
	// `offsetParent` property will force a reflow and re-evaluate the CSS animation.
	// https://gist.github.com/paulirish/5d52fb081b3570c81e3a#box-metrics
	// https://github.com/chartjs/Chart.js/issues/4737
	expando.reflow = !!node.offsetParent;

	node.classList.add(CSS_RENDER_MONITOR);
}

function unwatchForRender(node) {
	var expando = node[EXPANDO_KEY] || {};
	var proxy = expando.renderProxy;

	if (proxy) {
		helpers$1.each(ANIMATION_START_EVENTS, function(type) {
			removeListener(node, type, proxy);
		});

		delete expando.renderProxy;
	}

	node.classList.remove(CSS_RENDER_MONITOR);
}

function addResizeListener(node, listener, chart) {
	var expando = node[EXPANDO_KEY] || (node[EXPANDO_KEY] = {});

	// Let's keep track of this added resizer and thus avoid DOM query when removing it.
	var resizer = expando.resizer = createResizer(throttled(function() {
		if (expando.resizer) {
			var container = chart.options.maintainAspectRatio && node.parentNode;
			var w = container ? container.clientWidth : 0;
			listener(createEvent('resize', chart));
			if (container && container.clientWidth < w && chart.canvas) {
				// If the container size shrank during chart resize, let's assume
				// scrollbar appeared. So we resize again with the scrollbar visible -
				// effectively making chart smaller and the scrollbar hidden again.
				// Because we are inside `throttled`, and currently `ticking`, scroll
				// events are ignored during this whole 2 resize process.
				// If we assumed wrong and something else happened, we are resizing
				// twice in a frame (potential performance issue)
				listener(createEvent('resize', chart));
			}
		}
	}));

	// The resizer needs to be attached to the node parent, so we first need to be
	// sure that `node` is attached to the DOM before injecting the resizer element.
	watchForRender(node, function() {
		if (expando.resizer) {
			var container = node.parentNode;
			if (container && container !== resizer.parentNode) {
				container.insertBefore(resizer, container.firstChild);
			}

			// The container size might have changed, let's reset the resizer state.
			resizer._reset();
		}
	});
}

function removeResizeListener(node) {
	var expando = node[EXPANDO_KEY] || {};
	var resizer = expando.resizer;

	delete expando.resizer;
	unwatchForRender(node);

	if (resizer && resizer.parentNode) {
		resizer.parentNode.removeChild(resizer);
	}
}

/**
 * Injects CSS styles inline if the styles are not already present.
 * @param {HTMLDocument|ShadowRoot} rootNode - the node to contain the <style>.
 * @param {string} css - the CSS to be injected.
 */
function injectCSS(rootNode, css) {
	// https://stackoverflow.com/q/3922139
	var expando = rootNode[EXPANDO_KEY] || (rootNode[EXPANDO_KEY] = {});
	if (!expando.containsStyles) {
		expando.containsStyles = true;
		css = '/* Chart.js */\n' + css;
		var style = document.createElement('style');
		style.setAttribute('type', 'text/css');
		style.appendChild(document.createTextNode(css));
		rootNode.appendChild(style);
	}
}

var platform_dom$2 = {
	/**
	 * When `true`, prevents the automatic injection of the stylesheet required to
	 * correctly detect when the chart is added to the DOM and then resized. This
	 * switch has been added to allow external stylesheet (`dist/Chart(.min)?.js`)
	 * to be manually imported to make this library compatible with any CSP.
	 * See https://github.com/chartjs/Chart.js/issues/5208
	 */
	disableCSSInjection: false,

	/**
	 * This property holds whether this platform is enabled for the current environment.
	 * Currently used by platform.js to select the proper implementation.
	 * @private
	 */
	_enabled: typeof window !== 'undefined' && typeof document !== 'undefined',

	/**
	 * Initializes resources that depend on platform options.
	 * @param {HTMLCanvasElement} canvas - The Canvas element.
	 * @private
	 */
	_ensureLoaded: function(canvas) {
		if (!this.disableCSSInjection) {
			// If the canvas is in a shadow DOM, then the styles must also be inserted
			// into the same shadow DOM.
			// https://github.com/chartjs/Chart.js/issues/5763
			var root = canvas.getRootNode ? canvas.getRootNode() : document;
			var targetNode = root.host ? root : document.head;
			injectCSS(targetNode, stylesheet);
		}
	},

	acquireContext: function(item, config) {
		if (typeof item === 'string') {
			item = document.getElementById(item);
		} else if (item.length) {
			// Support for array based queries (such as jQuery)
			item = item[0];
		}

		if (item && item.canvas) {
			// Support for any object associated to a canvas (including a context2d)
			item = item.canvas;
		}

		// To prevent canvas fingerprinting, some add-ons undefine the getContext
		// method, for example: https://github.com/kkapsner/CanvasBlocker
		// https://github.com/chartjs/Chart.js/issues/2807
		var context = item && item.getContext && item.getContext('2d');

		// `instanceof HTMLCanvasElement/CanvasRenderingContext2D` fails when the item is
		// inside an iframe or when running in a protected environment. We could guess the
		// types from their toString() value but let's keep things flexible and assume it's
		// a sufficient condition if the item has a context2D which has item as `canvas`.
		// https://github.com/chartjs/Chart.js/issues/3887
		// https://github.com/chartjs/Chart.js/issues/4102
		// https://github.com/chartjs/Chart.js/issues/4152
		if (context && context.canvas === item) {
			// Load platform resources on first chart creation, to make it possible to
			// import the library before setting platform options.
			this._ensureLoaded(item);
			initCanvas(item, config);
			return context;
		}

		return null;
	},

	releaseContext: function(context) {
		var canvas = context.canvas;
		if (!canvas[EXPANDO_KEY]) {
			return;
		}

		var initial = canvas[EXPANDO_KEY].initial;
		['height', 'width'].forEach(function(prop) {
			var value = initial[prop];
			if (helpers$1.isNullOrUndef(value)) {
				canvas.removeAttribute(prop);
			} else {
				canvas.setAttribute(prop, value);
			}
		});

		helpers$1.each(initial.style || {}, function(value, key) {
			canvas.style[key] = value;
		});

		// The canvas render size might have been changed (and thus the state stack discarded),
		// we can't use save() and restore() to restore the initial state. So make sure that at
		// least the canvas context is reset to the default state by setting the canvas width.
		// https://www.w3.org/TR/2011/WD-html5-20110525/the-canvas-element.html
		// eslint-disable-next-line no-self-assign
		canvas.width = canvas.width;

		delete canvas[EXPANDO_KEY];
	},

	addEventListener: function(chart, type, listener) {
		var canvas = chart.canvas;
		if (type === 'resize') {
			// Note: the resize event is not supported on all browsers.
			addResizeListener(canvas, listener, chart);
			return;
		}

		var expando = listener[EXPANDO_KEY] || (listener[EXPANDO_KEY] = {});
		var proxies = expando.proxies || (expando.proxies = {});
		var proxy = proxies[chart.id + '_' + type] = function(event) {
			listener(fromNativeEvent(event, chart));
		};

		addListener(canvas, type, proxy);
	},

	removeEventListener: function(chart, type, listener) {
		var canvas = chart.canvas;
		if (type === 'resize') {
			// Note: the resize event is not supported on all browsers.
			removeResizeListener(canvas);
			return;
		}

		var expando = listener[EXPANDO_KEY] || {};
		var proxies = expando.proxies || {};
		var proxy = proxies[chart.id + '_' + type];
		if (!proxy) {
			return;
		}

		removeListener(canvas, type, proxy);
	}
};

// DEPRECATIONS

/**
 * Provided for backward compatibility, use EventTarget.addEventListener instead.
 * EventTarget.addEventListener compatibility: Chrome, Opera 7, Safari, FF1.5+, IE9+
 * @see https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener
 * @function Chart.helpers.addEvent
 * @deprecated since version 2.7.0
 * @todo remove at version 3
 * @private
 */
helpers$1.addEvent = addListener;

/**
 * Provided for backward compatibility, use EventTarget.removeEventListener instead.
 * EventTarget.removeEventListener compatibility: Chrome, Opera 7, Safari, FF1.5+, IE9+
 * @see https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/removeEventListener
 * @function Chart.helpers.removeEvent
 * @deprecated since version 2.7.0
 * @todo remove at version 3
 * @private
 */
helpers$1.removeEvent = removeListener;

// @TODO Make possible to select another platform at build time.
var implementation = platform_dom$2._enabled ? platform_dom$2 : platform_basic;

/**
 * @namespace Chart.platform
 * @see https://chartjs.gitbooks.io/proposals/content/Platform.html
 * @since 2.4.0
 */
var platform = helpers$1.extend({
	/**
	 * @since 2.7.0
	 */
	initialize: function() {},

	/**
	 * Called at chart construction time, returns a context2d instance implementing
	 * the [W3C Canvas 2D Context API standard]{@link https://www.w3.org/TR/2dcontext/}.
	 * @param {*} item - The native item from which to acquire context (platform specific)
	 * @param {object} options - The chart options
	 * @returns {CanvasRenderingContext2D} context2d instance
	 */
	acquireContext: function() {},

	/**
	 * Called at chart destruction time, releases any resources associated to the context
	 * previously returned by the acquireContext() method.
	 * @param {CanvasRenderingContext2D} context - The context2d instance
	 * @returns {boolean} true if the method succeeded, else false
	 */
	releaseContext: function() {},

	/**
	 * Registers the specified listener on the given chart.
	 * @param {Chart} chart - Chart from which to listen for event
	 * @param {string} type - The ({@link IEvent}) type to listen for
	 * @param {function} listener - Receives a notification (an object that implements
	 * the {@link IEvent} interface) when an event of the specified type occurs.
	 */
	addEventListener: function() {},

	/**
	 * Removes the specified listener previously registered with addEventListener.
	 * @param {Chart} chart - Chart from which to remove the listener
	 * @param {string} type - The ({@link IEvent}) type to remove
	 * @param {function} listener - The listener function to remove from the event target.
	 */
	removeEventListener: function() {}

}, implementation);

core_defaults._set('global', {
	plugins: {}
});

/**
 * The plugin service singleton
 * @namespace Chart.plugins
 * @since 2.1.0
 */
var core_plugins = {
	/**
	 * Globally registered plugins.
	 * @private
	 */
	_plugins: [],

	/**
	 * This identifier is used to invalidate the descriptors cache attached to each chart
	 * when a global plugin is registered or unregistered. In this case, the cache ID is
	 * incremented and descriptors are regenerated during following API calls.
	 * @private
	 */
	_cacheId: 0,

	/**
	 * Registers the given plugin(s) if not already registered.
	 * @param {IPlugin[]|IPlugin} plugins plugin instance(s).
	 */
	register: function(plugins) {
		var p = this._plugins;
		([]).concat(plugins).forEach(function(plugin) {
			if (p.indexOf(plugin) === -1) {
				p.push(plugin);
			}
		});

		this._cacheId++;
	},

	/**
	 * Unregisters the given plugin(s) only if registered.
	 * @param {IPlugin[]|IPlugin} plugins plugin instance(s).
	 */
	unregister: function(plugins) {
		var p = this._plugins;
		([]).concat(plugins).forEach(function(plugin) {
			var idx = p.indexOf(plugin);
			if (idx !== -1) {
				p.splice(idx, 1);
			}
		});

		this._cacheId++;
	},

	/**
	 * Remove all registered plugins.
	 * @since 2.1.5
	 */
	clear: function() {
		this._plugins = [];
		this._cacheId++;
	},

	/**
	 * Returns the number of registered plugins?
	 * @returns {number}
	 * @since 2.1.5
	 */
	count: function() {
		return this._plugins.length;
	},

	/**
	 * Returns all registered plugin instances.
	 * @returns {IPlugin[]} array of plugin objects.
	 * @since 2.1.5
	 */
	getAll: function() {
		return this._plugins;
	},

	/**
	 * Calls enabled plugins for `chart` on the specified hook and with the given args.
	 * This method immediately returns as soon as a plugin explicitly returns false. The
	 * returned value can be used, for instance, to interrupt the current action.
	 * @param {Chart} chart - The chart instance for which plugins should be called.
	 * @param {string} hook - The name of the plugin method to call (e.g. 'beforeUpdate').
	 * @param {Array} [args] - Extra arguments to apply to the hook call.
	 * @returns {boolean} false if any of the plugins return false, else returns true.
	 */
	notify: function(chart, hook, args) {
		var descriptors = this.descriptors(chart);
		var ilen = descriptors.length;
		var i, descriptor, plugin, params, method;

		for (i = 0; i < ilen; ++i) {
			descriptor = descriptors[i];
			plugin = descriptor.plugin;
			method = plugin[hook];
			if (typeof method === 'function') {
				params = [chart].concat(args || []);
				params.push(descriptor.options);
				if (method.apply(plugin, params) === false) {
					return false;
				}
			}
		}

		return true;
	},

	/**
	 * Returns descriptors of enabled plugins for the given chart.
	 * @returns {object[]} [{ plugin, options }]
	 * @private
	 */
	descriptors: function(chart) {
		var cache = chart.$plugins || (chart.$plugins = {});
		if (cache.id === this._cacheId) {
			return cache.descriptors;
		}

		var plugins = [];
		var descriptors = [];
		var config = (chart && chart.config) || {};
		var options = (config.options && config.options.plugins) || {};

		this._plugins.concat(config.plugins || []).forEach(function(plugin) {
			var idx = plugins.indexOf(plugin);
			if (idx !== -1) {
				return;
			}

			var id = plugin.id;
			var opts = options[id];
			if (opts === false) {
				return;
			}

			if (opts === true) {
				opts = helpers$1.clone(core_defaults.global.plugins[id]);
			}

			plugins.push(plugin);
			descriptors.push({
				plugin: plugin,
				options: opts || {}
			});
		});

		cache.descriptors = descriptors;
		cache.id = this._cacheId;
		return descriptors;
	},

	/**
	 * Invalidates cache for the given chart: descriptors hold a reference on plugin option,
	 * but in some cases, this reference can be changed by the user when updating options.
	 * https://github.com/chartjs/Chart.js/issues/5111#issuecomment-355934167
	 * @private
	 */
	_invalidate: function(chart) {
		delete chart.$plugins;
	}
};

var core_scaleService = {
	// Scale registration object. Extensions can register new scale types (such as log or DB scales) and then
	// use the new chart options to grab the correct scale
	constructors: {},
	// Use a registration function so that we can move to an ES6 map when we no longer need to support
	// old browsers

	// Scale config defaults
	defaults: {},
	registerScaleType: function(type, scaleConstructor, scaleDefaults) {
		this.constructors[type] = scaleConstructor;
		this.defaults[type] = helpers$1.clone(scaleDefaults);
	},
	getScaleConstructor: function(type) {
		return this.constructors.hasOwnProperty(type) ? this.constructors[type] : undefined;
	},
	getScaleDefaults: function(type) {
		// Return the scale defaults merged with the global settings so that we always use the latest ones
		return this.defaults.hasOwnProperty(type) ? helpers$1.merge(Object.create(null), [core_defaults.scale, this.defaults[type]]) : {};
	},
	updateScaleDefaults: function(type, additions) {
		var me = this;
		if (me.defaults.hasOwnProperty(type)) {
			me.defaults[type] = helpers$1.extend(me.defaults[type], additions);
		}
	},
	addScalesToLayout: function(chart) {
		// Adds each scale to the chart.boxes array to be sized accordingly
		helpers$1.each(chart.scales, function(scale) {
			// Set ILayoutItem parameters for backwards compatibility
			scale.fullWidth = scale.options.fullWidth;
			scale.position = scale.options.position;
			scale.weight = scale.options.weight;
			core_layouts.addBox(chart, scale);
		});
	}
};

var valueOrDefault$8 = helpers$1.valueOrDefault;
var getRtlHelper = helpers$1.rtl.getRtlAdapter;

core_defaults._set('global', {
	tooltips: {
		enabled: true,
		custom: null,
		mode: 'nearest',
		position: 'average',
		intersect: true,
		backgroundColor: 'rgba(0,0,0,0.8)',
		titleFontStyle: 'bold',
		titleSpacing: 2,
		titleMarginBottom: 6,
		titleFontColor: '#fff',
		titleAlign: 'left',
		bodySpacing: 2,
		bodyFontColor: '#fff',
		bodyAlign: 'left',
		footerFontStyle: 'bold',
		footerSpacing: 2,
		footerMarginTop: 6,
		footerFontColor: '#fff',
		footerAlign: 'left',
		yPadding: 6,
		xPadding: 6,
		caretPadding: 2,
		caretSize: 5,
		cornerRadius: 6,
		multiKeyBackground: '#fff',
		displayColors: true,
		borderColor: 'rgba(0,0,0,0)',
		borderWidth: 0,
		callbacks: {
			// Args are: (tooltipItems, data)
			beforeTitle: helpers$1.noop,
			title: function(tooltipItems, data) {
				var title = '';
				var labels = data.labels;
				var labelCount = labels ? labels.length : 0;

				if (tooltipItems.length > 0) {
					var item = tooltipItems[0];
					if (item.label) {
						title = item.label;
					} else if (item.xLabel) {
						title = item.xLabel;
					} else if (labelCount > 0 && item.index < labelCount) {
						title = labels[item.index];
					}
				}

				return title;
			},
			afterTitle: helpers$1.noop,

			// Args are: (tooltipItems, data)
			beforeBody: helpers$1.noop,

			// Args are: (tooltipItem, data)
			beforeLabel: helpers$1.noop,
			label: function(tooltipItem, data) {
				var label = data.datasets[tooltipItem.datasetIndex].label || '';

				if (label) {
					label += ': ';
				}
				if (!helpers$1.isNullOrUndef(tooltipItem.value)) {
					label += tooltipItem.value;
				} else {
					label += tooltipItem.yLabel;
				}
				return label;
			},
			labelColor: function(tooltipItem, chart) {
				var meta = chart.getDatasetMeta(tooltipItem.datasetIndex);
				var activeElement = meta.data[tooltipItem.index];
				var view = activeElement._view;
				return {
					borderColor: view.borderColor,
					backgroundColor: view.backgroundColor
				};
			},
			labelTextColor: function() {
				return this._options.bodyFontColor;
			},
			afterLabel: helpers$1.noop,

			// Args are: (tooltipItems, data)
			afterBody: helpers$1.noop,

			// Args are: (tooltipItems, data)
			beforeFooter: helpers$1.noop,
			footer: helpers$1.noop,
			afterFooter: helpers$1.noop
		}
	}
});

var positioners = {
	/**
	 * Average mode places the tooltip at the average position of the elements shown
	 * @function Chart.Tooltip.positioners.average
	 * @param elements {ChartElement[]} the elements being displayed in the tooltip
	 * @returns {object} tooltip position
	 */
	average: function(elements) {
		if (!elements.length) {
			return false;
		}

		var i, len;
		var x = 0;
		var y = 0;
		var count = 0;

		for (i = 0, len = elements.length; i < len; ++i) {
			var el = elements[i];
			if (el && el.hasValue()) {
				var pos = el.tooltipPosition();
				x += pos.x;
				y += pos.y;
				++count;
			}
		}

		return {
			x: x / count,
			y: y / count
		};
	},

	/**
	 * Gets the tooltip position nearest of the item nearest to the event position
	 * @function Chart.Tooltip.positioners.nearest
	 * @param elements {Chart.Element[]} the tooltip elements
	 * @param eventPosition {object} the position of the event in canvas coordinates
	 * @returns {object} the tooltip position
	 */
	nearest: function(elements, eventPosition) {
		var x = eventPosition.x;
		var y = eventPosition.y;
		var minDistance = Number.POSITIVE_INFINITY;
		var i, len, nearestElement;

		for (i = 0, len = elements.length; i < len; ++i) {
			var el = elements[i];
			if (el && el.hasValue()) {
				var center = el.getCenterPoint();
				var d = helpers$1.distanceBetweenPoints(eventPosition, center);

				if (d < minDistance) {
					minDistance = d;
					nearestElement = el;
				}
			}
		}

		if (nearestElement) {
			var tp = nearestElement.tooltipPosition();
			x = tp.x;
			y = tp.y;
		}

		return {
			x: x,
			y: y
		};
	}
};

// Helper to push or concat based on if the 2nd parameter is an array or not
function pushOrConcat(base, toPush) {
	if (toPush) {
		if (helpers$1.isArray(toPush)) {
			// base = base.concat(toPush);
			Array.prototype.push.apply(base, toPush);
		} else {
			base.push(toPush);
		}
	}

	return base;
}

/**
 * Returns array of strings split by newline
 * @param {string} value - The value to split by newline.
 * @returns {string[]} value if newline present - Returned from String split() method
 * @function
 */
function splitNewlines(str) {
	if ((typeof str === 'string' || str instanceof String) && str.indexOf('\n') > -1) {
		return str.split('\n');
	}
	return str;
}


/**
 * Private helper to create a tooltip item model
 * @param element - the chart element (point, arc, bar) to create the tooltip item for
 * @return new tooltip item
 */
function createTooltipItem(element) {
	var xScale = element._xScale;
	var yScale = element._yScale || element._scale; // handle radar || polarArea charts
	var index = element._index;
	var datasetIndex = element._datasetIndex;
	var controller = element._chart.getDatasetMeta(datasetIndex).controller;
	var indexScale = controller._getIndexScale();
	var valueScale = controller._getValueScale();

	return {
		xLabel: xScale ? xScale.getLabelForIndex(index, datasetIndex) : '',
		yLabel: yScale ? yScale.getLabelForIndex(index, datasetIndex) : '',
		label: indexScale ? '' + indexScale.getLabelForIndex(index, datasetIndex) : '',
		value: valueScale ? '' + valueScale.getLabelForIndex(index, datasetIndex) : '',
		index: index,
		datasetIndex: datasetIndex,
		x: element._model.x,
		y: element._model.y
	};
}

/**
 * Helper to get the reset model for the tooltip
 * @param tooltipOpts {object} the tooltip options
 */
function getBaseModel(tooltipOpts) {
	var globalDefaults = core_defaults.global;

	return {
		// Positioning
		xPadding: tooltipOpts.xPadding,
		yPadding: tooltipOpts.yPadding,
		xAlign: tooltipOpts.xAlign,
		yAlign: tooltipOpts.yAlign,

		// Drawing direction and text direction
		rtl: tooltipOpts.rtl,
		textDirection: tooltipOpts.textDirection,

		// Body
		bodyFontColor: tooltipOpts.bodyFontColor,
		_bodyFontFamily: valueOrDefault$8(tooltipOpts.bodyFontFamily, globalDefaults.defaultFontFamily),
		_bodyFontStyle: valueOrDefault$8(tooltipOpts.bodyFontStyle, globalDefaults.defaultFontStyle),
		_bodyAlign: tooltipOpts.bodyAlign,
		bodyFontSize: valueOrDefault$8(tooltipOpts.bodyFontSize, globalDefaults.defaultFontSize),
		bodySpacing: tooltipOpts.bodySpacing,

		// Title
		titleFontColor: tooltipOpts.titleFontColor,
		_titleFontFamily: valueOrDefault$8(tooltipOpts.titleFontFamily, globalDefaults.defaultFontFamily),
		_titleFontStyle: valueOrDefault$8(tooltipOpts.titleFontStyle, globalDefaults.defaultFontStyle),
		titleFontSize: valueOrDefault$8(tooltipOpts.titleFontSize, globalDefaults.defaultFontSize),
		_titleAlign: tooltipOpts.titleAlign,
		titleSpacing: tooltipOpts.titleSpacing,
		titleMarginBottom: tooltipOpts.titleMarginBottom,

		// Footer
		footerFontColor: tooltipOpts.footerFontColor,
		_footerFontFamily: valueOrDefault$8(tooltipOpts.footerFontFamily, globalDefaults.defaultFontFamily),
		_footerFontStyle: valueOrDefault$8(tooltipOpts.footerFontStyle, globalDefaults.defaultFontStyle),
		footerFontSize: valueOrDefault$8(tooltipOpts.footerFontSize, globalDefaults.defaultFontSize),
		_footerAlign: tooltipOpts.footerAlign,
		footerSpacing: tooltipOpts.footerSpacing,
		footerMarginTop: tooltipOpts.footerMarginTop,

		// Appearance
		caretSize: tooltipOpts.caretSize,
		cornerRadius: tooltipOpts.cornerRadius,
		backgroundColor: tooltipOpts.backgroundColor,
		opacity: 0,
		legendColorBackground: tooltipOpts.multiKeyBackground,
		displayColors: tooltipOpts.displayColors,
		borderColor: tooltipOpts.borderColor,
		borderWidth: tooltipOpts.borderWidth
	};
}

/**
 * Get the size of the tooltip
 */
function getTooltipSize(tooltip, model) {
	var ctx = tooltip._chart.ctx;

	var height = model.yPadding * 2; // Tooltip Padding
	var width = 0;

	// Count of all lines in the body
	var body = model.body;
	var combinedBodyLength = body.reduce(function(count, bodyItem) {
		return count + bodyItem.before.length + bodyItem.lines.length + bodyItem.after.length;
	}, 0);
	combinedBodyLength += model.beforeBody.length + model.afterBody.length;

	var titleLineCount = model.title.length;
	var footerLineCount = model.footer.length;
	var titleFontSize = model.titleFontSize;
	var bodyFontSize = model.bodyFontSize;
	var footerFontSize = model.footerFontSize;

	height += titleLineCount * titleFontSize; // Title Lines
	height += titleLineCount ? (titleLineCount - 1) * model.titleSpacing : 0; // Title Line Spacing
	height += titleLineCount ? model.titleMarginBottom : 0; // Title's bottom Margin
	height += combinedBodyLength * bodyFontSize; // Body Lines
	height += combinedBodyLength ? (combinedBodyLength - 1) * model.bodySpacing : 0; // Body Line Spacing
	height += footerLineCount ? model.footerMarginTop : 0; // Footer Margin
	height += footerLineCount * (footerFontSize); // Footer Lines
	height += footerLineCount ? (footerLineCount - 1) * model.footerSpacing : 0; // Footer Line Spacing

	// Title width
	var widthPadding = 0;
	var maxLineWidth = function(line) {
		width = Math.max(width, ctx.measureText(line).width + widthPadding);
	};

	ctx.font = helpers$1.fontString(titleFontSize, model._titleFontStyle, model._titleFontFamily);
	helpers$1.each(model.title, maxLineWidth);

	// Body width
	ctx.font = helpers$1.fontString(bodyFontSize, model._bodyFontStyle, model._bodyFontFamily);
	helpers$1.each(model.beforeBody.concat(model.afterBody), maxLineWidth);

	// Body lines may include some extra width due to the color box
	widthPadding = model.displayColors ? (bodyFontSize + 2) : 0;
	helpers$1.each(body, function(bodyItem) {
		helpers$1.each(bodyItem.before, maxLineWidth);
		helpers$1.each(bodyItem.lines, maxLineWidth);
		helpers$1.each(bodyItem.after, maxLineWidth);
	});

	// Reset back to 0
	widthPadding = 0;

	// Footer width
	ctx.font = helpers$1.fontString(footerFontSize, model._footerFontStyle, model._footerFontFamily);
	helpers$1.each(model.footer, maxLineWidth);

	// Add padding
	width += 2 * model.xPadding;

	return {
		width: width,
		height: height
	};
}

/**
 * Helper to get the alignment of a tooltip given the size
 */
function determineAlignment(tooltip, size) {
	var model = tooltip._model;
	var chart = tooltip._chart;
	var chartArea = tooltip._chart.chartArea;
	var xAlign = 'center';
	var yAlign = 'center';

	if (model.y < size.height) {
		yAlign = 'top';
	} else if (model.y > (chart.height - size.height)) {
		yAlign = 'bottom';
	}

	var lf, rf; // functions to determine left, right alignment
	var olf, orf; // functions to determine if left/right alignment causes tooltip to go outside chart
	var yf; // function to get the y alignment if the tooltip goes outside of the left or right edges
	var midX = (chartArea.left + chartArea.right) / 2;
	var midY = (chartArea.top + chartArea.bottom) / 2;

	if (yAlign === 'center') {
		lf = function(x) {
			return x <= midX;
		};
		rf = function(x) {
			return x > midX;
		};
	} else {
		lf = function(x) {
			return x <= (size.width / 2);
		};
		rf = function(x) {
			return x >= (chart.width - (size.width / 2));
		};
	}

	olf = function(x) {
		return x + size.width + model.caretSize + model.caretPadding > chart.width;
	};
	orf = function(x) {
		return x - size.width - model.caretSize - model.caretPadding < 0;
	};
	yf = function(y) {
		return y <= midY ? 'top' : 'bottom';
	};

	if (lf(model.x)) {
		xAlign = 'left';

		// Is tooltip too wide and goes over the right side of the chart.?
		if (olf(model.x)) {
			xAlign = 'center';
			yAlign = yf(model.y);
		}
	} else if (rf(model.x)) {
		xAlign = 'right';

		// Is tooltip too wide and goes outside left edge of canvas?
		if (orf(model.x)) {
			xAlign = 'center';
			yAlign = yf(model.y);
		}
	}

	var opts = tooltip._options;
	return {
		xAlign: opts.xAlign ? opts.xAlign : xAlign,
		yAlign: opts.yAlign ? opts.yAlign : yAlign
	};
}

/**
 * Helper to get the location a tooltip needs to be placed at given the initial position (via the vm) and the size and alignment
 */
function getBackgroundPoint(vm, size, alignment, chart) {
	// Background Position
	var x = vm.x;
	var y = vm.y;

	var caretSize = vm.caretSize;
	var caretPadding = vm.caretPadding;
	var cornerRadius = vm.cornerRadius;
	var xAlign = alignment.xAlign;
	var yAlign = alignment.yAlign;
	var paddingAndSize = caretSize + caretPadding;
	var radiusAndPadding = cornerRadius + caretPadding;

	if (xAlign === 'right') {
		x -= size.width;
	} else if (xAlign === 'center') {
		x -= (size.width / 2);
		if (x + size.width > chart.width) {
			x = chart.width - size.width;
		}
		if (x < 0) {
			x = 0;
		}
	}

	if (yAlign === 'top') {
		y += paddingAndSize;
	} else if (yAlign === 'bottom') {
		y -= size.height + paddingAndSize;
	} else {
		y -= (size.height / 2);
	}

	if (yAlign === 'center') {
		if (xAlign === 'left') {
			x += paddingAndSize;
		} else if (xAlign === 'right') {
			x -= paddingAndSize;
		}
	} else if (xAlign === 'left') {
		x -= radiusAndPadding;
	} else if (xAlign === 'right') {
		x += radiusAndPadding;
	}

	return {
		x: x,
		y: y
	};
}

function getAlignedX(vm, align) {
	return align === 'center'
		? vm.x + vm.width / 2
		: align === 'right'
			? vm.x + vm.width - vm.xPadding
			: vm.x + vm.xPadding;
}

/**
 * Helper to build before and after body lines
 */
function getBeforeAfterBodyLines(callback) {
	return pushOrConcat([], splitNewlines(callback));
}

var exports$4 = core_element.extend({
	initialize: function() {
		this._model = getBaseModel(this._options);
		this._lastActive = [];
	},

	// Get the title
	// Args are: (tooltipItem, data)
	getTitle: function() {
		var me = this;
		var opts = me._options;
		var callbacks = opts.callbacks;

		var beforeTitle = callbacks.beforeTitle.apply(me, arguments);
		var title = callbacks.title.apply(me, arguments);
		var afterTitle = callbacks.afterTitle.apply(me, arguments);

		var lines = [];
		lines = pushOrConcat(lines, splitNewlines(beforeTitle));
		lines = pushOrConcat(lines, splitNewlines(title));
		lines = pushOrConcat(lines, splitNewlines(afterTitle));

		return lines;
	},

	// Args are: (tooltipItem, data)
	getBeforeBody: function() {
		return getBeforeAfterBodyLines(this._options.callbacks.beforeBody.apply(this, arguments));
	},

	// Args are: (tooltipItem, data)
	getBody: function(tooltipItems, data) {
		var me = this;
		var callbacks = me._options.callbacks;
		var bodyItems = [];

		helpers$1.each(tooltipItems, function(tooltipItem) {
			var bodyItem = {
				before: [],
				lines: [],
				after: []
			};
			pushOrConcat(bodyItem.before, splitNewlines(callbacks.beforeLabel.call(me, tooltipItem, data)));
			pushOrConcat(bodyItem.lines, callbacks.label.call(me, tooltipItem, data));
			pushOrConcat(bodyItem.after, splitNewlines(callbacks.afterLabel.call(me, tooltipItem, data)));

			bodyItems.push(bodyItem);
		});

		return bodyItems;
	},

	// Args are: (tooltipItem, data)
	getAfterBody: function() {
		return getBeforeAfterBodyLines(this._options.callbacks.afterBody.apply(this, arguments));
	},

	// Get the footer and beforeFooter and afterFooter lines
	// Args are: (tooltipItem, data)
	getFooter: function() {
		var me = this;
		var callbacks = me._options.callbacks;

		var beforeFooter = callbacks.beforeFooter.apply(me, arguments);
		var footer = callbacks.footer.apply(me, arguments);
		var afterFooter = callbacks.afterFooter.apply(me, arguments);

		var lines = [];
		lines = pushOrConcat(lines, splitNewlines(beforeFooter));
		lines = pushOrConcat(lines, splitNewlines(footer));
		lines = pushOrConcat(lines, splitNewlines(afterFooter));

		return lines;
	},

	update: function(changed) {
		var me = this;
		var opts = me._options;

		// Need to regenerate the model because its faster than using extend and it is necessary due to the optimization in Chart.Element.transition
		// that does _view = _model if ease === 1. This causes the 2nd tooltip update to set properties in both the view and model at the same time
		// which breaks any animations.
		var existingModel = me._model;
		var model = me._model = getBaseModel(opts);
		var active = me._active;

		var data = me._data;

		// In the case where active.length === 0 we need to keep these at existing values for good animations
		var alignment = {
			xAlign: existingModel.xAlign,
			yAlign: existingModel.yAlign
		};
		var backgroundPoint = {
			x: existingModel.x,
			y: existingModel.y
		};
		var tooltipSize = {
			width: existingModel.width,
			height: existingModel.height
		};
		var tooltipPosition = {
			x: existingModel.caretX,
			y: existingModel.caretY
		};

		var i, len;

		if (active.length) {
			model.opacity = 1;

			var labelColors = [];
			var labelTextColors = [];
			tooltipPosition = positioners[opts.position].call(me, active, me._eventPosition);

			var tooltipItems = [];
			for (i = 0, len = active.length; i < len; ++i) {
				tooltipItems.push(createTooltipItem(active[i]));
			}

			// If the user provided a filter function, use it to modify the tooltip items
			if (opts.filter) {
				tooltipItems = tooltipItems.filter(function(a) {
					return opts.filter(a, data);
				});
			}

			// If the user provided a sorting function, use it to modify the tooltip items
			if (opts.itemSort) {
				tooltipItems = tooltipItems.sort(function(a, b) {
					return opts.itemSort(a, b, data);
				});
			}

			// Determine colors for boxes
			helpers$1.each(tooltipItems, function(tooltipItem) {
				labelColors.push(opts.callbacks.labelColor.call(me, tooltipItem, me._chart));
				labelTextColors.push(opts.callbacks.labelTextColor.call(me, tooltipItem, me._chart));
			});


			// Build the Text Lines
			model.title = me.getTitle(tooltipItems, data);
			model.beforeBody = me.getBeforeBody(tooltipItems, data);
			model.body = me.getBody(tooltipItems, data);
			model.afterBody = me.getAfterBody(tooltipItems, data);
			model.footer = me.getFooter(tooltipItems, data);

			// Initial positioning and colors
			model.x = tooltipPosition.x;
			model.y = tooltipPosition.y;
			model.caretPadding = opts.caretPadding;
			model.labelColors = labelColors;
			model.labelTextColors = labelTextColors;

			// data points
			model.dataPoints = tooltipItems;

			// We need to determine alignment of the tooltip
			tooltipSize = getTooltipSize(this, model);
			alignment = determineAlignment(this, tooltipSize);
			// Final Size and Position
			backgroundPoint = getBackgroundPoint(model, tooltipSize, alignment, me._chart);
		} else {
			model.opacity = 0;
		}

		model.xAlign = alignment.xAlign;
		model.yAlign = alignment.yAlign;
		model.x = backgroundPoint.x;
		model.y = backgroundPoint.y;
		model.width = tooltipSize.width;
		model.height = tooltipSize.height;

		// Point where the caret on the tooltip points to
		model.caretX = tooltipPosition.x;
		model.caretY = tooltipPosition.y;

		me._model = model;

		if (changed && opts.custom) {
			opts.custom.call(me, model);
		}

		return me;
	},

	drawCaret: function(tooltipPoint, size) {
		var ctx = this._chart.ctx;
		var vm = this._view;
		var caretPosition = this.getCaretPosition(tooltipPoint, size, vm);

		ctx.lineTo(caretPosition.x1, caretPosition.y1);
		ctx.lineTo(caretPosition.x2, caretPosition.y2);
		ctx.lineTo(caretPosition.x3, caretPosition.y3);
	},
	getCaretPosition: function(tooltipPoint, size, vm) {
		var x1, x2, x3, y1, y2, y3;
		var caretSize = vm.caretSize;
		var cornerRadius = vm.cornerRadius;
		var xAlign = vm.xAlign;
		var yAlign = vm.yAlign;
		var ptX = tooltipPoint.x;
		var ptY = tooltipPoint.y;
		var width = size.width;
		var height = size.height;

		if (yAlign === 'center') {
			y2 = ptY + (height / 2);

			if (xAlign === 'left') {
				x1 = ptX;
				x2 = x1 - caretSize;
				x3 = x1;

				y1 = y2 + caretSize;
				y3 = y2 - caretSize;
			} else {
				x1 = ptX + width;
				x2 = x1 + caretSize;
				x3 = x1;

				y1 = y2 - caretSize;
				y3 = y2 + caretSize;
			}
		} else {
			if (xAlign === 'left') {
				x2 = ptX + cornerRadius + (caretSize);
				x1 = x2 - caretSize;
				x3 = x2 + caretSize;
			} else if (xAlign === 'right') {
				x2 = ptX + width - cornerRadius - caretSize;
				x1 = x2 - caretSize;
				x3 = x2 + caretSize;
			} else {
				x2 = vm.caretX;
				x1 = x2 - caretSize;
				x3 = x2 + caretSize;
			}
			if (yAlign === 'top') {
				y1 = ptY;
				y2 = y1 - caretSize;
				y3 = y1;
			} else {
				y1 = ptY + height;
				y2 = y1 + caretSize;
				y3 = y1;
				// invert drawing order
				var tmp = x3;
				x3 = x1;
				x1 = tmp;
			}
		}
		return {x1: x1, x2: x2, x3: x3, y1: y1, y2: y2, y3: y3};
	},

	drawTitle: function(pt, vm, ctx) {
		var title = vm.title;
		var length = title.length;
		var titleFontSize, titleSpacing, i;

		if (length) {
			var rtlHelper = getRtlHelper(vm.rtl, vm.x, vm.width);

			pt.x = getAlignedX(vm, vm._titleAlign);

			ctx.textAlign = rtlHelper.textAlign(vm._titleAlign);
			ctx.textBaseline = 'middle';

			titleFontSize = vm.titleFontSize;
			titleSpacing = vm.titleSpacing;

			ctx.fillStyle = vm.titleFontColor;
			ctx.font = helpers$1.fontString(titleFontSize, vm._titleFontStyle, vm._titleFontFamily);

			for (i = 0; i < length; ++i) {
				ctx.fillText(title[i], rtlHelper.x(pt.x), pt.y + titleFontSize / 2);
				pt.y += titleFontSize + titleSpacing; // Line Height and spacing

				if (i + 1 === length) {
					pt.y += vm.titleMarginBottom - titleSpacing; // If Last, add margin, remove spacing
				}
			}
		}
	},

	drawBody: function(pt, vm, ctx) {
		var bodyFontSize = vm.bodyFontSize;
		var bodySpacing = vm.bodySpacing;
		var bodyAlign = vm._bodyAlign;
		var body = vm.body;
		var drawColorBoxes = vm.displayColors;
		var xLinePadding = 0;
		var colorX = drawColorBoxes ? getAlignedX(vm, 'left') : 0;

		var rtlHelper = getRtlHelper(vm.rtl, vm.x, vm.width);

		var fillLineOfText = function(line) {
			ctx.fillText(line, rtlHelper.x(pt.x + xLinePadding), pt.y + bodyFontSize / 2);
			pt.y += bodyFontSize + bodySpacing;
		};

		var bodyItem, textColor, labelColors, lines, i, j, ilen, jlen;
		var bodyAlignForCalculation = rtlHelper.textAlign(bodyAlign);

		ctx.textAlign = bodyAlign;
		ctx.textBaseline = 'middle';
		ctx.font = helpers$1.fontString(bodyFontSize, vm._bodyFontStyle, vm._bodyFontFamily);

		pt.x = getAlignedX(vm, bodyAlignForCalculation);

		// Before body lines
		ctx.fillStyle = vm.bodyFontColor;
		helpers$1.each(vm.beforeBody, fillLineOfText);

		xLinePadding = drawColorBoxes && bodyAlignForCalculation !== 'right'
			? bodyAlign === 'center' ? (bodyFontSize / 2 + 1) : (bodyFontSize + 2)
			: 0;

		// Draw body lines now
		for (i = 0, ilen = body.length; i < ilen; ++i) {
			bodyItem = body[i];
			textColor = vm.labelTextColors[i];
			labelColors = vm.labelColors[i];

			ctx.fillStyle = textColor;
			helpers$1.each(bodyItem.before, fillLineOfText);

			lines = bodyItem.lines;
			for (j = 0, jlen = lines.length; j < jlen; ++j) {
				// Draw Legend-like boxes if needed
				if (drawColorBoxes) {
					var rtlColorX = rtlHelper.x(colorX);

					// Fill a white rect so that colours merge nicely if the opacity is < 1
					ctx.fillStyle = vm.legendColorBackground;
					ctx.fillRect(rtlHelper.leftForLtr(rtlColorX, bodyFontSize), pt.y, bodyFontSize, bodyFontSize);

					// Border
					ctx.lineWidth = 1;
					ctx.strokeStyle = labelColors.borderColor;
					ctx.strokeRect(rtlHelper.leftForLtr(rtlColorX, bodyFontSize), pt.y, bodyFontSize, bodyFontSize);

					// Inner square
					ctx.fillStyle = labelColors.backgroundColor;
					ctx.fillRect(rtlHelper.leftForLtr(rtlHelper.xPlus(rtlColorX, 1), bodyFontSize - 2), pt.y + 1, bodyFontSize - 2, bodyFontSize - 2);
					ctx.fillStyle = textColor;
				}

				fillLineOfText(lines[j]);
			}

			helpers$1.each(bodyItem.after, fillLineOfText);
		}

		// Reset back to 0 for after body
		xLinePadding = 0;

		// After body lines
		helpers$1.each(vm.afterBody, fillLineOfText);
		pt.y -= bodySpacing; // Remove last body spacing
	},

	drawFooter: function(pt, vm, ctx) {
		var footer = vm.footer;
		var length = footer.length;
		var footerFontSize, i;

		if (length) {
			var rtlHelper = getRtlHelper(vm.rtl, vm.x, vm.width);

			pt.x = getAlignedX(vm, vm._footerAlign);
			pt.y += vm.footerMarginTop;

			ctx.textAlign = rtlHelper.textAlign(vm._footerAlign);
			ctx.textBaseline = 'middle';

			footerFontSize = vm.footerFontSize;

			ctx.fillStyle = vm.footerFontColor;
			ctx.font = helpers$1.fontString(footerFontSize, vm._footerFontStyle, vm._footerFontFamily);

			for (i = 0; i < length; ++i) {
				ctx.fillText(footer[i], rtlHelper.x(pt.x), pt.y + footerFontSize / 2);
				pt.y += footerFontSize + vm.footerSpacing;
			}
		}
	},

	drawBackground: function(pt, vm, ctx, tooltipSize) {
		ctx.fillStyle = vm.backgroundColor;
		ctx.strokeStyle = vm.borderColor;
		ctx.lineWidth = vm.borderWidth;
		var xAlign = vm.xAlign;
		var yAlign = vm.yAlign;
		var x = pt.x;
		var y = pt.y;
		var width = tooltipSize.width;
		var height = tooltipSize.height;
		var radius = vm.cornerRadius;

		ctx.beginPath();
		ctx.moveTo(x + radius, y);
		if (yAlign === 'top') {
			this.drawCaret(pt, tooltipSize);
		}
		ctx.lineTo(x + width - radius, y);
		ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
		if (yAlign === 'center' && xAlign === 'right') {
			this.drawCaret(pt, tooltipSize);
		}
		ctx.lineTo(x + width, y + height - radius);
		ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
		if (yAlign === 'bottom') {
			this.drawCaret(pt, tooltipSize);
		}
		ctx.lineTo(x + radius, y + height);
		ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
		if (yAlign === 'center' && xAlign === 'left') {
			this.drawCaret(pt, tooltipSize);
		}
		ctx.lineTo(x, y + radius);
		ctx.quadraticCurveTo(x, y, x + radius, y);
		ctx.closePath();

		ctx.fill();

		if (vm.borderWidth > 0) {
			ctx.stroke();
		}
	},

	draw: function() {
		var ctx = this._chart.ctx;
		var vm = this._view;

		if (vm.opacity === 0) {
			return;
		}

		var tooltipSize = {
			width: vm.width,
			height: vm.height
		};
		var pt = {
			x: vm.x,
			y: vm.y
		};

		// IE11/Edge does not like very small opacities, so snap to 0
		var opacity = Math.abs(vm.opacity < 1e-3) ? 0 : vm.opacity;

		// Truthy/falsey value for empty tooltip
		var hasTooltipContent = vm.title.length || vm.beforeBody.length || vm.body.length || vm.afterBody.length || vm.footer.length;

		if (this._options.enabled && hasTooltipContent) {
			ctx.save();
			ctx.globalAlpha = opacity;

			// Draw Background
			this.drawBackground(pt, vm, ctx, tooltipSize);

			// Draw Title, Body, and Footer
			pt.y += vm.yPadding;

			helpers$1.rtl.overrideTextDirection(ctx, vm.textDirection);

			// Titles
			this.drawTitle(pt, vm, ctx);

			// Body
			this.drawBody(pt, vm, ctx);

			// Footer
			this.drawFooter(pt, vm, ctx);

			helpers$1.rtl.restoreTextDirection(ctx, vm.textDirection);

			ctx.restore();
		}
	},

	/**
	 * Handle an event
	 * @private
	 * @param {IEvent} event - The event to handle
	 * @returns {boolean} true if the tooltip changed
	 */
	handleEvent: function(e) {
		var me = this;
		var options = me._options;
		var changed = false;

		me._lastActive = me._lastActive || [];

		// Find Active Elements for tooltips
		if (e.type === 'mouseout') {
			me._active = [];
		} else {
			me._active = me._chart.getElementsAtEventForMode(e, options.mode, options);
			if (options.reverse) {
				me._active.reverse();
			}
		}

		// Remember Last Actives
		changed = !helpers$1.arrayEquals(me._active, me._lastActive);

		// Only handle target event on tooltip change
		if (changed) {
			me._lastActive = me._active;

			if (options.enabled || options.custom) {
				me._eventPosition = {
					x: e.x,
					y: e.y
				};

				me.update(true);
				me.pivot();
			}
		}

		return changed;
	}
});

/**
 * @namespace Chart.Tooltip.positioners
 */
var positioners_1 = positioners;

var core_tooltip = exports$4;
core_tooltip.positioners = positioners_1;

var valueOrDefault$9 = helpers$1.valueOrDefault;

core_defaults._set('global', {
	elements: {},
	events: [
		'mousemove',
		'mouseout',
		'click',
		'touchstart',
		'touchmove'
	],
	hover: {
		onHover: null,
		mode: 'nearest',
		intersect: true,
		animationDuration: 400
	},
	onClick: null,
	maintainAspectRatio: true,
	responsive: true,
	responsiveAnimationDuration: 0
});

/**
 * Recursively merge the given config objects representing the `scales` option
 * by incorporating scale defaults in `xAxes` and `yAxes` array items, then
 * returns a deep copy of the result, thus doesn't alter inputs.
 */
function mergeScaleConfig(/* config objects ... */) {
	return helpers$1.merge(Object.create(null), [].slice.call(arguments), {
		merger: function(key, target, source, options) {
			if (key === 'xAxes' || key === 'yAxes') {
				var slen = source[key].length;
				var i, type, scale;

				if (!target[key]) {
					target[key] = [];
				}

				for (i = 0; i < slen; ++i) {
					scale = source[key][i];
					type = valueOrDefault$9(scale.type, key === 'xAxes' ? 'category' : 'linear');

					if (i >= target[key].length) {
						target[key].push({});
					}

					if (!target[key][i].type || (scale.type && scale.type !== target[key][i].type)) {
						// new/untyped scale or type changed: let's apply the new defaults
						// then merge source scale to correctly overwrite the defaults.
						helpers$1.merge(target[key][i], [core_scaleService.getScaleDefaults(type), scale]);
					} else {
						// scales type are the same
						helpers$1.merge(target[key][i], scale);
					}
				}
			} else {
				helpers$1._merger(key, target, source, options);
			}
		}
	});
}

/**
 * Recursively merge the given config objects as the root options by handling
 * default scale options for the `scales` and `scale` properties, then returns
 * a deep copy of the result, thus doesn't alter inputs.
 */
function mergeConfig(/* config objects ... */) {
	return helpers$1.merge(Object.create(null), [].slice.call(arguments), {
		merger: function(key, target, source, options) {
			var tval = target[key] || Object.create(null);
			var sval = source[key];

			if (key === 'scales') {
				// scale config merging is complex. Add our own function here for that
				target[key] = mergeScaleConfig(tval, sval);
			} else if (key === 'scale') {
				// used in polar area & radar charts since there is only one scale
				target[key] = helpers$1.merge(tval, [core_scaleService.getScaleDefaults(sval.type), sval]);
			} else {
				helpers$1._merger(key, target, source, options);
			}
		}
	});
}

function initConfig(config) {
	config = config || Object.create(null);

	// Do NOT use mergeConfig for the data object because this method merges arrays
	// and so would change references to labels and datasets, preventing data updates.
	var data = config.data = config.data || {};
	data.datasets = data.datasets || [];
	data.labels = data.labels || [];

	config.options = mergeConfig(
		core_defaults.global,
		core_defaults[config.type],
		config.options || {});

	return config;
}

function updateConfig(chart) {
	var newOptions = chart.options;

	helpers$1.each(chart.scales, function(scale) {
		core_layouts.removeBox(chart, scale);
	});

	newOptions = mergeConfig(
		core_defaults.global,
		core_defaults[chart.config.type],
		newOptions);

	chart.options = chart.config.options = newOptions;
	chart.ensureScalesHaveIDs();
	chart.buildOrUpdateScales();

	// Tooltip
	chart.tooltip._options = newOptions.tooltips;
	chart.tooltip.initialize();
}

function nextAvailableScaleId(axesOpts, prefix, index) {
	var id;
	var hasId = function(obj) {
		return obj.id === id;
	};

	do {
		id = prefix + index++;
	} while (helpers$1.findIndex(axesOpts, hasId) >= 0);

	return id;
}

function positionIsHorizontal(position) {
	return position === 'top' || position === 'bottom';
}

function compare2Level(l1, l2) {
	return function(a, b) {
		return a[l1] === b[l1]
			? a[l2] - b[l2]
			: a[l1] - b[l1];
	};
}

var Chart = function(item, config) {
	this.construct(item, config);
	return this;
};

helpers$1.extend(Chart.prototype, /** @lends Chart */ {
	/**
	 * @private
	 */
	construct: function(item, config) {
		var me = this;

		config = initConfig(config);

		var context = platform.acquireContext(item, config);
		var canvas = context && context.canvas;
		var height = canvas && canvas.height;
		var width = canvas && canvas.width;

		me.id = helpers$1.uid();
		me.ctx = context;
		me.canvas = canvas;
		me.config = config;
		me.width = width;
		me.height = height;
		me.aspectRatio = height ? width / height : null;
		me.options = config.options;
		me._bufferedRender = false;
		me._layers = [];

		/**
		 * Provided for backward compatibility, Chart and Chart.Controller have been merged,
		 * the "instance" still need to be defined since it might be called from plugins.
		 * @prop Chart#chart
		 * @deprecated since version 2.6.0
		 * @todo remove at version 3
		 * @private
		 */
		me.chart = me;
		me.controller = me; // chart.chart.controller #inception

		// Add the chart instance to the global namespace
		Chart.instances[me.id] = me;

		// Define alias to the config data: `chart.data === chart.config.data`
		Object.defineProperty(me, 'data', {
			get: function() {
				return me.config.data;
			},
			set: function(value) {
				me.config.data = value;
			}
		});

		if (!context || !canvas) {
			// The given item is not a compatible context2d element, let's return before finalizing
			// the chart initialization but after setting basic chart / controller properties that
			// can help to figure out that the chart is not valid (e.g chart.canvas !== null);
			// https://github.com/chartjs/Chart.js/issues/2807
			console.error("Failed to create chart: can't acquire context from the given item");
			return;
		}

		me.initialize();
		me.update();
	},

	/**
	 * @private
	 */
	initialize: function() {
		var me = this;

		// Before init plugin notification
		core_plugins.notify(me, 'beforeInit');

		helpers$1.retinaScale(me, me.options.devicePixelRatio);

		me.bindEvents();

		if (me.options.responsive) {
			// Initial resize before chart draws (must be silent to preserve initial animations).
			me.resize(true);
		}

		me.initToolTip();

		// After init plugin notification
		core_plugins.notify(me, 'afterInit');

		return me;
	},

	clear: function() {
		helpers$1.canvas.clear(this);
		return this;
	},

	stop: function() {
		// Stops any current animation loop occurring
		core_animations.cancelAnimation(this);
		return this;
	},

	resize: function(silent) {
		var me = this;
		var options = me.options;
		var canvas = me.canvas;
		var aspectRatio = (options.maintainAspectRatio && me.aspectRatio) || null;

		// the canvas render width and height will be casted to integers so make sure that
		// the canvas display style uses the same integer values to avoid blurring effect.

		// Set to 0 instead of canvas.size because the size defaults to 300x150 if the element is collapsed
		var newWidth = Math.max(0, Math.floor(helpers$1.getMaximumWidth(canvas)));
		var newHeight = Math.max(0, Math.floor(aspectRatio ? newWidth / aspectRatio : helpers$1.getMaximumHeight(canvas)));

		if (me.width === newWidth && me.height === newHeight) {
			return;
		}

		canvas.width = me.width = newWidth;
		canvas.height = me.height = newHeight;
		canvas.style.width = newWidth + 'px';
		canvas.style.height = newHeight + 'px';

		helpers$1.retinaScale(me, options.devicePixelRatio);

		if (!silent) {
			// Notify any plugins about the resize
			var newSize = {width: newWidth, height: newHeight};
			core_plugins.notify(me, 'resize', [newSize]);

			// Notify of resize
			if (options.onResize) {
				options.onResize(me, newSize);
			}

			me.stop();
			me.update({
				duration: options.responsiveAnimationDuration
			});
		}
	},

	ensureScalesHaveIDs: function() {
		var options = this.options;
		var scalesOptions = options.scales || {};
		var scaleOptions = options.scale;

		helpers$1.each(scalesOptions.xAxes, function(xAxisOptions, index) {
			if (!xAxisOptions.id) {
				xAxisOptions.id = nextAvailableScaleId(scalesOptions.xAxes, 'x-axis-', index);
			}
		});

		helpers$1.each(scalesOptions.yAxes, function(yAxisOptions, index) {
			if (!yAxisOptions.id) {
				yAxisOptions.id = nextAvailableScaleId(scalesOptions.yAxes, 'y-axis-', index);
			}
		});

		if (scaleOptions) {
			scaleOptions.id = scaleOptions.id || 'scale';
		}
	},

	/**
	 * Builds a map of scale ID to scale object for future lookup.
	 */
	buildOrUpdateScales: function() {
		var me = this;
		var options = me.options;
		var scales = me.scales || {};
		var items = [];
		var updated = Object.keys(scales).reduce(function(obj, id) {
			obj[id] = false;
			return obj;
		}, {});

		if (options.scales) {
			items = items.concat(
				(options.scales.xAxes || []).map(function(xAxisOptions) {
					return {options: xAxisOptions, dtype: 'category', dposition: 'bottom'};
				}),
				(options.scales.yAxes || []).map(function(yAxisOptions) {
					return {options: yAxisOptions, dtype: 'linear', dposition: 'left'};
				})
			);
		}

		if (options.scale) {
			items.push({
				options: options.scale,
				dtype: 'radialLinear',
				isDefault: true,
				dposition: 'chartArea'
			});
		}

		helpers$1.each(items, function(item) {
			var scaleOptions = item.options;
			var id = scaleOptions.id;
			var scaleType = valueOrDefault$9(scaleOptions.type, item.dtype);

			if (positionIsHorizontal(scaleOptions.position) !== positionIsHorizontal(item.dposition)) {
				scaleOptions.position = item.dposition;
			}

			updated[id] = true;
			var scale = null;
			if (id in scales && scales[id].type === scaleType) {
				scale = scales[id];
				scale.options = scaleOptions;
				scale.ctx = me.ctx;
				scale.chart = me;
			} else {
				var scaleClass = core_scaleService.getScaleConstructor(scaleType);
				if (!scaleClass) {
					return;
				}
				scale = new scaleClass({
					id: id,
					type: scaleType,
					options: scaleOptions,
					ctx: me.ctx,
					chart: me
				});
				scales[scale.id] = scale;
			}

			scale.mergeTicksOptions();

			// TODO(SB): I think we should be able to remove this custom case (options.scale)
			// and consider it as a regular scale part of the "scales"" map only! This would
			// make the logic easier and remove some useless? custom code.
			if (item.isDefault) {
				me.scale = scale;
			}
		});
		// clear up discarded scales
		helpers$1.each(updated, function(hasUpdated, id) {
			if (!hasUpdated) {
				delete scales[id];
			}
		});

		me.scales = scales;

		core_scaleService.addScalesToLayout(this);
	},

	buildOrUpdateControllers: function() {
		var me = this;
		var newControllers = [];
		var datasets = me.data.datasets;
		var i, ilen;

		for (i = 0, ilen = datasets.length; i < ilen; i++) {
			var dataset = datasets[i];
			var meta = me.getDatasetMeta(i);
			var type = dataset.type || me.config.type;

			if (meta.type && meta.type !== type) {
				me.destroyDatasetMeta(i);
				meta = me.getDatasetMeta(i);
			}
			meta.type = type;
			meta.order = dataset.order || 0;
			meta.index = i;

			if (meta.controller) {
				meta.controller.updateIndex(i);
				meta.controller.linkScales();
			} else {
				var ControllerClass = controllers[meta.type];
				if (ControllerClass === undefined) {
					throw new Error('"' + meta.type + '" is not a chart type.');
				}

				meta.controller = new ControllerClass(me, i);
				newControllers.push(meta.controller);
			}
		}

		return newControllers;
	},

	/**
	 * Reset the elements of all datasets
	 * @private
	 */
	resetElements: function() {
		var me = this;
		helpers$1.each(me.data.datasets, function(dataset, datasetIndex) {
			me.getDatasetMeta(datasetIndex).controller.reset();
		}, me);
	},

	/**
	* Resets the chart back to it's state before the initial animation
	*/
	reset: function() {
		this.resetElements();
		this.tooltip.initialize();
	},

	update: function(config) {
		var me = this;
		var i, ilen;

		if (!config || typeof config !== 'object') {
			// backwards compatibility
			config = {
				duration: config,
				lazy: arguments[1]
			};
		}

		updateConfig(me);

		// plugins options references might have change, let's invalidate the cache
		// https://github.com/chartjs/Chart.js/issues/5111#issuecomment-355934167
		core_plugins._invalidate(me);

		if (core_plugins.notify(me, 'beforeUpdate') === false) {
			return;
		}

		// In case the entire data object changed
		me.tooltip._data = me.data;

		// Make sure dataset controllers are updated and new controllers are reset
		var newControllers = me.buildOrUpdateControllers();

		// Make sure all dataset controllers have correct meta data counts
		for (i = 0, ilen = me.data.datasets.length; i < ilen; i++) {
			me.getDatasetMeta(i).controller.buildOrUpdateElements();
		}

		me.updateLayout();

		// Can only reset the new controllers after the scales have been updated
		if (me.options.animation && me.options.animation.duration) {
			helpers$1.each(newControllers, function(controller) {
				controller.reset();
			});
		}

		me.updateDatasets();

		// Need to reset tooltip in case it is displayed with elements that are removed
		// after update.
		me.tooltip.initialize();

		// Last active contains items that were previously in the tooltip.
		// When we reset the tooltip, we need to clear it
		me.lastActive = [];

		// Do this before render so that any plugins that need final scale updates can use it
		core_plugins.notify(me, 'afterUpdate');

		me._layers.sort(compare2Level('z', '_idx'));

		if (me._bufferedRender) {
			me._bufferedRequest = {
				duration: config.duration,
				easing: config.easing,
				lazy: config.lazy
			};
		} else {
			me.render(config);
		}
	},

	/**
	 * Updates the chart layout unless a plugin returns `false` to the `beforeLayout`
	 * hook, in which case, plugins will not be called on `afterLayout`.
	 * @private
	 */
	updateLayout: function() {
		var me = this;

		if (core_plugins.notify(me, 'beforeLayout') === false) {
			return;
		}

		core_layouts.update(this, this.width, this.height);

		me._layers = [];
		helpers$1.each(me.boxes, function(box) {
			// _configure is called twice, once in core.scale.update and once here.
			// Here the boxes are fully updated and at their final positions.
			if (box._configure) {
				box._configure();
			}
			me._layers.push.apply(me._layers, box._layers());
		}, me);

		me._layers.forEach(function(item, index) {
			item._idx = index;
		});

		/**
		 * Provided for backward compatibility, use `afterLayout` instead.
		 * @method IPlugin#afterScaleUpdate
		 * @deprecated since version 2.5.0
		 * @todo remove at version 3
		 * @private
		 */
		core_plugins.notify(me, 'afterScaleUpdate');
		core_plugins.notify(me, 'afterLayout');
	},

	/**
	 * Updates all datasets unless a plugin returns `false` to the `beforeDatasetsUpdate`
	 * hook, in which case, plugins will not be called on `afterDatasetsUpdate`.
	 * @private
	 */
	updateDatasets: function() {
		var me = this;

		if (core_plugins.notify(me, 'beforeDatasetsUpdate') === false) {
			return;
		}

		for (var i = 0, ilen = me.data.datasets.length; i < ilen; ++i) {
			me.updateDataset(i);
		}

		core_plugins.notify(me, 'afterDatasetsUpdate');
	},

	/**
	 * Updates dataset at index unless a plugin returns `false` to the `beforeDatasetUpdate`
	 * hook, in which case, plugins will not be called on `afterDatasetUpdate`.
	 * @private
	 */
	updateDataset: function(index) {
		var me = this;
		var meta = me.getDatasetMeta(index);
		var args = {
			meta: meta,
			index: index
		};

		if (core_plugins.notify(me, 'beforeDatasetUpdate', [args]) === false) {
			return;
		}

		meta.controller._update();

		core_plugins.notify(me, 'afterDatasetUpdate', [args]);
	},

	render: function(config) {
		var me = this;

		if (!config || typeof config !== 'object') {
			// backwards compatibility
			config = {
				duration: config,
				lazy: arguments[1]
			};
		}

		var animationOptions = me.options.animation;
		var duration = valueOrDefault$9(config.duration, animationOptions && animationOptions.duration);
		var lazy = config.lazy;

		if (core_plugins.notify(me, 'beforeRender') === false) {
			return;
		}

		var onComplete = function(animation) {
			core_plugins.notify(me, 'afterRender');
			helpers$1.callback(animationOptions && animationOptions.onComplete, [animation], me);
		};

		if (animationOptions && duration) {
			var animation = new core_animation({
				numSteps: duration / 16.66, // 60 fps
				easing: config.easing || animationOptions.easing,

				render: function(chart, animationObject) {
					var easingFunction = helpers$1.easing.effects[animationObject.easing];
					var currentStep = animationObject.currentStep;
					var stepDecimal = currentStep / animationObject.numSteps;

					chart.draw(easingFunction(stepDecimal), stepDecimal, currentStep);
				},

				onAnimationProgress: animationOptions.onProgress,
				onAnimationComplete: onComplete
			});

			core_animations.addAnimation(me, animation, duration, lazy);
		} else {
			me.draw();

			// See https://github.com/chartjs/Chart.js/issues/3781
			onComplete(new core_animation({numSteps: 0, chart: me}));
		}

		return me;
	},

	draw: function(easingValue) {
		var me = this;
		var i, layers;

		me.clear();

		if (helpers$1.isNullOrUndef(easingValue)) {
			easingValue = 1;
		}

		me.transition(easingValue);

		if (me.width <= 0 || me.height <= 0) {
			return;
		}

		if (core_plugins.notify(me, 'beforeDraw', [easingValue]) === false) {
			return;
		}

		// Because of plugin hooks (before/afterDatasetsDraw), datasets can't
		// currently be part of layers. Instead, we draw
		// layers <= 0 before(default, backward compat), and the rest after
		layers = me._layers;
		for (i = 0; i < layers.length && layers[i].z <= 0; ++i) {
			layers[i].draw(me.chartArea);
		}

		me.drawDatasets(easingValue);

		// Rest of layers
		for (; i < layers.length; ++i) {
			layers[i].draw(me.chartArea);
		}

		me._drawTooltip(easingValue);

		core_plugins.notify(me, 'afterDraw', [easingValue]);
	},

	/**
	 * @private
	 */
	transition: function(easingValue) {
		var me = this;

		for (var i = 0, ilen = (me.data.datasets || []).length; i < ilen; ++i) {
			if (me.isDatasetVisible(i)) {
				me.getDatasetMeta(i).controller.transition(easingValue);
			}
		}

		me.tooltip.transition(easingValue);
	},

	/**
	 * @private
	 */
	_getSortedDatasetMetas: function(filterVisible) {
		var me = this;
		var datasets = me.data.datasets || [];
		var result = [];
		var i, ilen;

		for (i = 0, ilen = datasets.length; i < ilen; ++i) {
			if (!filterVisible || me.isDatasetVisible(i)) {
				result.push(me.getDatasetMeta(i));
			}
		}

		result.sort(compare2Level('order', 'index'));

		return result;
	},

	/**
	 * @private
	 */
	_getSortedVisibleDatasetMetas: function() {
		return this._getSortedDatasetMetas(true);
	},

	/**
	 * Draws all datasets unless a plugin returns `false` to the `beforeDatasetsDraw`
	 * hook, in which case, plugins will not be called on `afterDatasetsDraw`.
	 * @private
	 */
	drawDatasets: function(easingValue) {
		var me = this;
		var metasets, i;

		if (core_plugins.notify(me, 'beforeDatasetsDraw', [easingValue]) === false) {
			return;
		}

		metasets = me._getSortedVisibleDatasetMetas();
		for (i = metasets.length - 1; i >= 0; --i) {
			me.drawDataset(metasets[i], easingValue);
		}

		core_plugins.notify(me, 'afterDatasetsDraw', [easingValue]);
	},

	/**
	 * Draws dataset at index unless a plugin returns `false` to the `beforeDatasetDraw`
	 * hook, in which case, plugins will not be called on `afterDatasetDraw`.
	 * @private
	 */
	drawDataset: function(meta, easingValue) {
		var me = this;
		var args = {
			meta: meta,
			index: meta.index,
			easingValue: easingValue
		};

		if (core_plugins.notify(me, 'beforeDatasetDraw', [args]) === false) {
			return;
		}

		meta.controller.draw(easingValue);

		core_plugins.notify(me, 'afterDatasetDraw', [args]);
	},

	/**
	 * Draws tooltip unless a plugin returns `false` to the `beforeTooltipDraw`
	 * hook, in which case, plugins will not be called on `afterTooltipDraw`.
	 * @private
	 */
	_drawTooltip: function(easingValue) {
		var me = this;
		var tooltip = me.tooltip;
		var args = {
			tooltip: tooltip,
			easingValue: easingValue
		};

		if (core_plugins.notify(me, 'beforeTooltipDraw', [args]) === false) {
			return;
		}

		tooltip.draw();

		core_plugins.notify(me, 'afterTooltipDraw', [args]);
	},

	/**
	 * Get the single element that was clicked on
	 * @return An object containing the dataset index and element index of the matching element. Also contains the rectangle that was draw
	 */
	getElementAtEvent: function(e) {
		return core_interaction.modes.single(this, e);
	},

	getElementsAtEvent: function(e) {
		return core_interaction.modes.label(this, e, {intersect: true});
	},

	getElementsAtXAxis: function(e) {
		return core_interaction.modes['x-axis'](this, e, {intersect: true});
	},

	getElementsAtEventForMode: function(e, mode, options) {
		var method = core_interaction.modes[mode];
		if (typeof method === 'function') {
			return method(this, e, options);
		}

		return [];
	},

	getDatasetAtEvent: function(e) {
		return core_interaction.modes.dataset(this, e, {intersect: true});
	},

	getDatasetMeta: function(datasetIndex) {
		var me = this;
		var dataset = me.data.datasets[datasetIndex];
		if (!dataset._meta) {
			dataset._meta = {};
		}

		var meta = dataset._meta[me.id];
		if (!meta) {
			meta = dataset._meta[me.id] = {
				type: null,
				data: [],
				dataset: null,
				controller: null,
				hidden: null,			// See isDatasetVisible() comment
				xAxisID: null,
				yAxisID: null,
				order: dataset.order || 0,
				index: datasetIndex
			};
		}

		return meta;
	},

	getVisibleDatasetCount: function() {
		var count = 0;
		for (var i = 0, ilen = this.data.datasets.length; i < ilen; ++i) {
			if (this.isDatasetVisible(i)) {
				count++;
			}
		}
		return count;
	},

	isDatasetVisible: function(datasetIndex) {
		var meta = this.getDatasetMeta(datasetIndex);

		// meta.hidden is a per chart dataset hidden flag override with 3 states: if true or false,
		// the dataset.hidden value is ignored, else if null, the dataset hidden state is returned.
		return typeof meta.hidden === 'boolean' ? !meta.hidden : !this.data.datasets[datasetIndex].hidden;
	},

	generateLegend: function() {
		return this.options.legendCallback(this);
	},

	/**
	 * @private
	 */
	destroyDatasetMeta: function(datasetIndex) {
		var id = this.id;
		var dataset = this.data.datasets[datasetIndex];
		var meta = dataset._meta && dataset._meta[id];

		if (meta) {
			meta.controller.destroy();
			delete dataset._meta[id];
		}
	},

	destroy: function() {
		var me = this;
		var canvas = me.canvas;
		var i, ilen;

		me.stop();

		// dataset controllers need to cleanup associated data
		for (i = 0, ilen = me.data.datasets.length; i < ilen; ++i) {
			me.destroyDatasetMeta(i);
		}

		if (canvas) {
			me.unbindEvents();
			helpers$1.canvas.clear(me);
			platform.releaseContext(me.ctx);
			me.canvas = null;
			me.ctx = null;
		}

		core_plugins.notify(me, 'destroy');

		delete Chart.instances[me.id];
	},

	toBase64Image: function() {
		return this.canvas.toDataURL.apply(this.canvas, arguments);
	},

	initToolTip: function() {
		var me = this;
		me.tooltip = new core_tooltip({
			_chart: me,
			_chartInstance: me, // deprecated, backward compatibility
			_data: me.data,
			_options: me.options.tooltips
		}, me);
	},

	/**
	 * @private
	 */
	bindEvents: function() {
		var me = this;
		var listeners = me._listeners = {};
		var listener = function() {
			me.eventHandler.apply(me, arguments);
		};

		helpers$1.each(me.options.events, function(type) {
			platform.addEventListener(me, type, listener);
			listeners[type] = listener;
		});

		// Elements used to detect size change should not be injected for non responsive charts.
		// See https://github.com/chartjs/Chart.js/issues/2210
		if (me.options.responsive) {
			listener = function() {
				me.resize();
			};

			platform.addEventListener(me, 'resize', listener);
			listeners.resize = listener;
		}
	},

	/**
	 * @private
	 */
	unbindEvents: function() {
		var me = this;
		var listeners = me._listeners;
		if (!listeners) {
			return;
		}

		delete me._listeners;
		helpers$1.each(listeners, function(listener, type) {
			platform.removeEventListener(me, type, listener);
		});
	},

	updateHoverStyle: function(elements, mode, enabled) {
		var prefix = enabled ? 'set' : 'remove';
		var element, i, ilen;

		for (i = 0, ilen = elements.length; i < ilen; ++i) {
			element = elements[i];
			if (element) {
				this.getDatasetMeta(element._datasetIndex).controller[prefix + 'HoverStyle'](element);
			}
		}

		if (mode === 'dataset') {
			this.getDatasetMeta(elements[0]._datasetIndex).controller['_' + prefix + 'DatasetHoverStyle']();
		}
	},

	/**
	 * @private
	 */
	eventHandler: function(e) {
		var me = this;
		var tooltip = me.tooltip;

		if (core_plugins.notify(me, 'beforeEvent', [e]) === false) {
			return;
		}

		// Buffer any update calls so that renders do not occur
		me._bufferedRender = true;
		me._bufferedRequest = null;

		var changed = me.handleEvent(e);
		// for smooth tooltip animations issue #4989
		// the tooltip should be the source of change
		// Animation check workaround:
		// tooltip._start will be null when tooltip isn't animating
		if (tooltip) {
			changed = tooltip._start
				? tooltip.handleEvent(e)
				: changed | tooltip.handleEvent(e);
		}

		core_plugins.notify(me, 'afterEvent', [e]);

		var bufferedRequest = me._bufferedRequest;
		if (bufferedRequest) {
			// If we have an update that was triggered, we need to do a normal render
			me.render(bufferedRequest);
		} else if (changed && !me.animating) {
			// If entering, leaving, or changing elements, animate the change via pivot
			me.stop();

			// We only need to render at this point. Updating will cause scales to be
			// recomputed generating flicker & using more memory than necessary.
			me.render({
				duration: me.options.hover.animationDuration,
				lazy: true
			});
		}

		me._bufferedRender = false;
		me._bufferedRequest = null;

		return me;
	},

	/**
	 * Handle an event
	 * @private
	 * @param {IEvent} event the event to handle
	 * @return {boolean} true if the chart needs to re-render
	 */
	handleEvent: function(e) {
		var me = this;
		var options = me.options || {};
		var hoverOptions = options.hover;
		var changed = false;

		me.lastActive = me.lastActive || [];

		// Find Active Elements for hover and tooltips
		if (e.type === 'mouseout') {
			me.active = [];
		} else {
			me.active = me.getElementsAtEventForMode(e, hoverOptions.mode, hoverOptions);
		}

		// Invoke onHover hook
		// Need to call with native event here to not break backwards compatibility
		helpers$1.callback(options.onHover || options.hover.onHover, [e.native, me.active], me);

		if (e.type === 'mouseup' || e.type === 'click') {
			if (options.onClick) {
				// Use e.native here for backwards compatibility
				options.onClick.call(me, e.native, me.active);
			}
		}

		// Remove styling for last active (even if it may still be active)
		if (me.lastActive.length) {
			me.updateHoverStyle(me.lastActive, hoverOptions.mode, false);
		}

		// Built in hover styling
		if (me.active.length && hoverOptions.mode) {
			me.updateHoverStyle(me.active, hoverOptions.mode, true);
		}

		changed = !helpers$1.arrayEquals(me.active, me.lastActive);

		// Remember Last Actives
		me.lastActive = me.active;

		return changed;
	}
});

/**
 * NOTE(SB) We actually don't use this container anymore but we need to keep it
 * for backward compatibility. Though, it can still be useful for plugins that
 * would need to work on multiple charts?!
 */
Chart.instances = {};

var core_controller = Chart;

// DEPRECATIONS

/**
 * Provided for backward compatibility, use Chart instead.
 * @class Chart.Controller
 * @deprecated since version 2.6
 * @todo remove at version 3
 * @private
 */
Chart.Controller = Chart;

/**
 * Provided for backward compatibility, not available anymore.
 * @namespace Chart
 * @deprecated since version 2.8
 * @todo remove at version 3
 * @private
 */
Chart.types = {};

/**
 * Provided for backward compatibility, not available anymore.
 * @namespace Chart.helpers.configMerge
 * @deprecated since version 2.8.0
 * @todo remove at version 3
 * @private
 */
helpers$1.configMerge = mergeConfig;

/**
 * Provided for backward compatibility, not available anymore.
 * @namespace Chart.helpers.scaleMerge
 * @deprecated since version 2.8.0
 * @todo remove at version 3
 * @private
 */
helpers$1.scaleMerge = mergeScaleConfig;

var core_helpers = function() {

	// -- Basic js utility methods

	helpers$1.where = function(collection, filterCallback) {
		if (helpers$1.isArray(collection) && Array.prototype.filter) {
			return collection.filter(filterCallback);
		}
		var filtered = [];

		helpers$1.each(collection, function(item) {
			if (filterCallback(item)) {
				filtered.push(item);
			}
		});

		return filtered;
	};
	helpers$1.findIndex = Array.prototype.findIndex ?
		function(array, callback, scope) {
			return array.findIndex(callback, scope);
		} :
		function(array, callback, scope) {
			scope = scope === undefined ? array : scope;
			for (var i = 0, ilen = array.length; i < ilen; ++i) {
				if (callback.call(scope, array[i], i, array)) {
					return i;
				}
			}
			return -1;
		};
	helpers$1.findNextWhere = function(arrayToSearch, filterCallback, startIndex) {
		// Default to start of the array
		if (helpers$1.isNullOrUndef(startIndex)) {
			startIndex = -1;
		}
		for (var i = startIndex + 1; i < arrayToSearch.length; i++) {
			var currentItem = arrayToSearch[i];
			if (filterCallback(currentItem)) {
				return currentItem;
			}
		}
	};
	helpers$1.findPreviousWhere = function(arrayToSearch, filterCallback, startIndex) {
		// Default to end of the array
		if (helpers$1.isNullOrUndef(startIndex)) {
			startIndex = arrayToSearch.length;
		}
		for (var i = startIndex - 1; i >= 0; i--) {
			var currentItem = arrayToSearch[i];
			if (filterCallback(currentItem)) {
				return currentItem;
			}
		}
	};

	// -- Math methods
	helpers$1.isNumber = function(n) {
		return !isNaN(parseFloat(n)) && isFinite(n);
	};
	helpers$1.almostEquals = function(x, y, epsilon) {
		return Math.abs(x - y) < epsilon;
	};
	helpers$1.almostWhole = function(x, epsilon) {
		var rounded = Math.round(x);
		return ((rounded - epsilon) <= x) && ((rounded + epsilon) >= x);
	};
	helpers$1.max = function(array) {
		return array.reduce(function(max, value) {
			if (!isNaN(value)) {
				return Math.max(max, value);
			}
			return max;
		}, Number.NEGATIVE_INFINITY);
	};
	helpers$1.min = function(array) {
		return array.reduce(function(min, value) {
			if (!isNaN(value)) {
				return Math.min(min, value);
			}
			return min;
		}, Number.POSITIVE_INFINITY);
	};
	helpers$1.sign = Math.sign ?
		function(x) {
			return Math.sign(x);
		} :
		function(x) {
			x = +x; // convert to a number
			if (x === 0 || isNaN(x)) {
				return x;
			}
			return x > 0 ? 1 : -1;
		};
	helpers$1.toRadians = function(degrees) {
		return degrees * (Math.PI / 180);
	};
	helpers$1.toDegrees = function(radians) {
		return radians * (180 / Math.PI);
	};

	/**
	 * Returns the number of decimal places
	 * i.e. the number of digits after the decimal point, of the value of this Number.
	 * @param {number} x - A number.
	 * @returns {number} The number of decimal places.
	 * @private
	 */
	helpers$1._decimalPlaces = function(x) {
		if (!helpers$1.isFinite(x)) {
			return;
		}
		var e = 1;
		var p = 0;
		while (Math.round(x * e) / e !== x) {
			e *= 10;
			p++;
		}
		return p;
	};

	// Gets the angle from vertical upright to the point about a centre.
	helpers$1.getAngleFromPoint = function(centrePoint, anglePoint) {
		var distanceFromXCenter = anglePoint.x - centrePoint.x;
		var distanceFromYCenter = anglePoint.y - centrePoint.y;
		var radialDistanceFromCenter = Math.sqrt(distanceFromXCenter * distanceFromXCenter + distanceFromYCenter * distanceFromYCenter);

		var angle = Math.atan2(distanceFromYCenter, distanceFromXCenter);

		if (angle < (-0.5 * Math.PI)) {
			angle += 2.0 * Math.PI; // make sure the returned angle is in the range of (-PI/2, 3PI/2]
		}

		return {
			angle: angle,
			distance: radialDistanceFromCenter
		};
	};
	helpers$1.distanceBetweenPoints = function(pt1, pt2) {
		return Math.sqrt(Math.pow(pt2.x - pt1.x, 2) + Math.pow(pt2.y - pt1.y, 2));
	};

	/**
	 * Provided for backward compatibility, not available anymore
	 * @function Chart.helpers.aliasPixel
	 * @deprecated since version 2.8.0
	 * @todo remove at version 3
	 */
	helpers$1.aliasPixel = function(pixelWidth) {
		return (pixelWidth % 2 === 0) ? 0 : 0.5;
	};

	/**
	 * Returns the aligned pixel value to avoid anti-aliasing blur
	 * @param {Chart} chart - The chart instance.
	 * @param {number} pixel - A pixel value.
	 * @param {number} width - The width of the element.
	 * @returns {number} The aligned pixel value.
	 * @private
	 */
	helpers$1._alignPixel = function(chart, pixel, width) {
		var devicePixelRatio = chart.currentDevicePixelRatio;
		var halfWidth = width / 2;
		return Math.round((pixel - halfWidth) * devicePixelRatio) / devicePixelRatio + halfWidth;
	};

	helpers$1.splineCurve = function(firstPoint, middlePoint, afterPoint, t) {
		// Props to Rob Spencer at scaled innovation for his post on splining between points
		// http://scaledinnovation.com/analytics/splines/aboutSplines.html

		// This function must also respect "skipped" points

		var previous = firstPoint.skip ? middlePoint : firstPoint;
		var current = middlePoint;
		var next = afterPoint.skip ? middlePoint : afterPoint;

		var d01 = Math.sqrt(Math.pow(current.x - previous.x, 2) + Math.pow(current.y - previous.y, 2));
		var d12 = Math.sqrt(Math.pow(next.x - current.x, 2) + Math.pow(next.y - current.y, 2));

		var s01 = d01 / (d01 + d12);
		var s12 = d12 / (d01 + d12);

		// If all points are the same, s01 & s02 will be inf
		s01 = isNaN(s01) ? 0 : s01;
		s12 = isNaN(s12) ? 0 : s12;

		var fa = t * s01; // scaling factor for triangle Ta
		var fb = t * s12;

		return {
			previous: {
				x: current.x - fa * (next.x - previous.x),
				y: current.y - fa * (next.y - previous.y)
			},
			next: {
				x: current.x + fb * (next.x - previous.x),
				y: current.y + fb * (next.y - previous.y)
			}
		};
	};
	helpers$1.EPSILON = Number.EPSILON || 1e-14;
	helpers$1.splineCurveMonotone = function(points) {
		// This function calculates Bézier control points in a similar way than |splineCurve|,
		// but preserves monotonicity of the provided data and ensures no local extremums are added
		// between the dataset discrete points due to the interpolation.
		// See : https://en.wikipedia.org/wiki/Monotone_cubic_interpolation

		var pointsWithTangents = (points || []).map(function(point) {
			return {
				model: point._model,
				deltaK: 0,
				mK: 0
			};
		});

		// Calculate slopes (deltaK) and initialize tangents (mK)
		var pointsLen = pointsWithTangents.length;
		var i, pointBefore, pointCurrent, pointAfter;
		for (i = 0; i < pointsLen; ++i) {
			pointCurrent = pointsWithTangents[i];
			if (pointCurrent.model.skip) {
				continue;
			}

			pointBefore = i > 0 ? pointsWithTangents[i - 1] : null;
			pointAfter = i < pointsLen - 1 ? pointsWithTangents[i + 1] : null;
			if (pointAfter && !pointAfter.model.skip) {
				var slopeDeltaX = (pointAfter.model.x - pointCurrent.model.x);

				// In the case of two points that appear at the same x pixel, slopeDeltaX is 0
				pointCurrent.deltaK = slopeDeltaX !== 0 ? (pointAfter.model.y - pointCurrent.model.y) / slopeDeltaX : 0;
			}

			if (!pointBefore || pointBefore.model.skip) {
				pointCurrent.mK = pointCurrent.deltaK;
			} else if (!pointAfter || pointAfter.model.skip) {
				pointCurrent.mK = pointBefore.deltaK;
			} else if (this.sign(pointBefore.deltaK) !== this.sign(pointCurrent.deltaK)) {
				pointCurrent.mK = 0;
			} else {
				pointCurrent.mK = (pointBefore.deltaK + pointCurrent.deltaK) / 2;
			}
		}

		// Adjust tangents to ensure monotonic properties
		var alphaK, betaK, tauK, squaredMagnitude;
		for (i = 0; i < pointsLen - 1; ++i) {
			pointCurrent = pointsWithTangents[i];
			pointAfter = pointsWithTangents[i + 1];
			if (pointCurrent.model.skip || pointAfter.model.skip) {
				continue;
			}

			if (helpers$1.almostEquals(pointCurrent.deltaK, 0, this.EPSILON)) {
				pointCurrent.mK = pointAfter.mK = 0;
				continue;
			}

			alphaK = pointCurrent.mK / pointCurrent.deltaK;
			betaK = pointAfter.mK / pointCurrent.deltaK;
			squaredMagnitude = Math.pow(alphaK, 2) + Math.pow(betaK, 2);
			if (squaredMagnitude <= 9) {
				continue;
			}

			tauK = 3 / Math.sqrt(squaredMagnitude);
			pointCurrent.mK = alphaK * tauK * pointCurrent.deltaK;
			pointAfter.mK = betaK * tauK * pointCurrent.deltaK;
		}

		// Compute control points
		var deltaX;
		for (i = 0; i < pointsLen; ++i) {
			pointCurrent = pointsWithTangents[i];
			if (pointCurrent.model.skip) {
				continue;
			}

			pointBefore = i > 0 ? pointsWithTangents[i - 1] : null;
			pointAfter = i < pointsLen - 1 ? pointsWithTangents[i + 1] : null;
			if (pointBefore && !pointBefore.model.skip) {
				deltaX = (pointCurrent.model.x - pointBefore.model.x) / 3;
				pointCurrent.model.controlPointPreviousX = pointCurrent.model.x - deltaX;
				pointCurrent.model.controlPointPreviousY = pointCurrent.model.y - deltaX * pointCurrent.mK;
			}
			if (pointAfter && !pointAfter.model.skip) {
				deltaX = (pointAfter.model.x - pointCurrent.model.x) / 3;
				pointCurrent.model.controlPointNextX = pointCurrent.model.x + deltaX;
				pointCurrent.model.controlPointNextY = pointCurrent.model.y + deltaX * pointCurrent.mK;
			}
		}
	};
	helpers$1.nextItem = function(collection, index, loop) {
		if (loop) {
			return index >= collection.length - 1 ? collection[0] : collection[index + 1];
		}
		return index >= collection.length - 1 ? collection[collection.length - 1] : collection[index + 1];
	};
	helpers$1.previousItem = function(collection, index, loop) {
		if (loop) {
			return index <= 0 ? collection[collection.length - 1] : collection[index - 1];
		}
		return index <= 0 ? collection[0] : collection[index - 1];
	};
	// Implementation of the nice number algorithm used in determining where axis labels will go
	helpers$1.niceNum = function(range, round) {
		var exponent = Math.floor(helpers$1.log10(range));
		var fraction = range / Math.pow(10, exponent);
		var niceFraction;

		if (round) {
			if (fraction < 1.5) {
				niceFraction = 1;
			} else if (fraction < 3) {
				niceFraction = 2;
			} else if (fraction < 7) {
				niceFraction = 5;
			} else {
				niceFraction = 10;
			}
		} else if (fraction <= 1.0) {
			niceFraction = 1;
		} else if (fraction <= 2) {
			niceFraction = 2;
		} else if (fraction <= 5) {
			niceFraction = 5;
		} else {
			niceFraction = 10;
		}

		return niceFraction * Math.pow(10, exponent);
	};
	// Request animation polyfill - https://www.paulirish.com/2011/requestanimationframe-for-smart-animating/
	helpers$1.requestAnimFrame = (function() {
		if (typeof window === 'undefined') {
			return function(callback) {
				callback();
			};
		}
		return window.requestAnimationFrame ||
			window.webkitRequestAnimationFrame ||
			window.mozRequestAnimationFrame ||
			window.oRequestAnimationFrame ||
			window.msRequestAnimationFrame ||
			function(callback) {
				return window.setTimeout(callback, 1000 / 60);
			};
	}());
	// -- DOM methods
	helpers$1.getRelativePosition = function(evt, chart) {
		var mouseX, mouseY;
		var e = evt.originalEvent || evt;
		var canvas = evt.target || evt.srcElement;
		var boundingRect = canvas.getBoundingClientRect();

		var touches = e.touches;
		if (touches && touches.length > 0) {
			mouseX = touches[0].clientX;
			mouseY = touches[0].clientY;

		} else {
			mouseX = e.clientX;
			mouseY = e.clientY;
		}

		// Scale mouse coordinates into canvas coordinates
		// by following the pattern laid out by 'jerryj' in the comments of
		// https://www.html5canvastutorials.com/advanced/html5-canvas-mouse-coordinates/
		var paddingLeft = parseFloat(helpers$1.getStyle(canvas, 'padding-left'));
		var paddingTop = parseFloat(helpers$1.getStyle(canvas, 'padding-top'));
		var paddingRight = parseFloat(helpers$1.getStyle(canvas, 'padding-right'));
		var paddingBottom = parseFloat(helpers$1.getStyle(canvas, 'padding-bottom'));
		var width = boundingRect.right - boundingRect.left - paddingLeft - paddingRight;
		var height = boundingRect.bottom - boundingRect.top - paddingTop - paddingBottom;

		// We divide by the current device pixel ratio, because the canvas is scaled up by that amount in each direction. However
		// the backend model is in unscaled coordinates. Since we are going to deal with our model coordinates, we go back here
		mouseX = Math.round((mouseX - boundingRect.left - paddingLeft) / (width) * canvas.width / chart.currentDevicePixelRatio);
		mouseY = Math.round((mouseY - boundingRect.top - paddingTop) / (height) * canvas.height / chart.currentDevicePixelRatio);

		return {
			x: mouseX,
			y: mouseY
		};

	};

	// Private helper function to convert max-width/max-height values that may be percentages into a number
	function parseMaxStyle(styleValue, node, parentProperty) {
		var valueInPixels;
		if (typeof styleValue === 'string') {
			valueInPixels = parseInt(styleValue, 10);

			if (styleValue.indexOf('%') !== -1) {
				// percentage * size in dimension
				valueInPixels = valueInPixels / 100 * node.parentNode[parentProperty];
			}
		} else {
			valueInPixels = styleValue;
		}

		return valueInPixels;
	}

	/**
	 * Returns if the given value contains an effective constraint.
	 * @private
	 */
	function isConstrainedValue(value) {
		return value !== undefined && value !== null && value !== 'none';
	}

	/**
	 * Returns the max width or height of the given DOM node in a cross-browser compatible fashion
	 * @param {HTMLElement} domNode - the node to check the constraint on
	 * @param {string} maxStyle - the style that defines the maximum for the direction we are using ('max-width' / 'max-height')
	 * @param {string} percentageProperty - property of parent to use when calculating width as a percentage
	 * @see {@link https://www.nathanaeljones.com/blog/2013/reading-max-width-cross-browser}
	 */
	function getConstraintDimension(domNode, maxStyle, percentageProperty) {
		var view = document.defaultView;
		var parentNode = helpers$1._getParentNode(domNode);
		var constrainedNode = view.getComputedStyle(domNode)[maxStyle];
		var constrainedContainer = view.getComputedStyle(parentNode)[maxStyle];
		var hasCNode = isConstrainedValue(constrainedNode);
		var hasCContainer = isConstrainedValue(constrainedContainer);
		var infinity = Number.POSITIVE_INFINITY;

		if (hasCNode || hasCContainer) {
			return Math.min(
				hasCNode ? parseMaxStyle(constrainedNode, domNode, percentageProperty) : infinity,
				hasCContainer ? parseMaxStyle(constrainedContainer, parentNode, percentageProperty) : infinity);
		}

		return 'none';
	}
	// returns Number or undefined if no constraint
	helpers$1.getConstraintWidth = function(domNode) {
		return getConstraintDimension(domNode, 'max-width', 'clientWidth');
	};
	// returns Number or undefined if no constraint
	helpers$1.getConstraintHeight = function(domNode) {
		return getConstraintDimension(domNode, 'max-height', 'clientHeight');
	};
	/**
	 * @private
 	 */
	helpers$1._calculatePadding = function(container, padding, parentDimension) {
		padding = helpers$1.getStyle(container, padding);

		return padding.indexOf('%') > -1 ? parentDimension * parseInt(padding, 10) / 100 : parseInt(padding, 10);
	};
	/**
	 * @private
	 */
	helpers$1._getParentNode = function(domNode) {
		var parent = domNode.parentNode;
		if (parent && parent.toString() === '[object ShadowRoot]') {
			parent = parent.host;
		}
		return parent;
	};
	helpers$1.getMaximumWidth = function(domNode) {
		var container = helpers$1._getParentNode(domNode);
		if (!container) {
			return domNode.clientWidth;
		}

		var clientWidth = container.clientWidth;
		var paddingLeft = helpers$1._calculatePadding(container, 'padding-left', clientWidth);
		var paddingRight = helpers$1._calculatePadding(container, 'padding-right', clientWidth);

		var w = clientWidth - paddingLeft - paddingRight;
		var cw = helpers$1.getConstraintWidth(domNode);
		return isNaN(cw) ? w : Math.min(w, cw);
	};
	helpers$1.getMaximumHeight = function(domNode) {
		var container = helpers$1._getParentNode(domNode);
		if (!container) {
			return domNode.clientHeight;
		}

		var clientHeight = container.clientHeight;
		var paddingTop = helpers$1._calculatePadding(container, 'padding-top', clientHeight);
		var paddingBottom = helpers$1._calculatePadding(container, 'padding-bottom', clientHeight);

		var h = clientHeight - paddingTop - paddingBottom;
		var ch = helpers$1.getConstraintHeight(domNode);
		return isNaN(ch) ? h : Math.min(h, ch);
	};
	helpers$1.getStyle = function(el, property) {
		return el.currentStyle ?
			el.currentStyle[property] :
			document.defaultView.getComputedStyle(el, null).getPropertyValue(property);
	};
	helpers$1.retinaScale = function(chart, forceRatio) {
		var pixelRatio = chart.currentDevicePixelRatio = forceRatio || (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
		if (pixelRatio === 1) {
			return;
		}

		var canvas = chart.canvas;
		var height = chart.height;
		var width = chart.width;

		canvas.height = height * pixelRatio;
		canvas.width = width * pixelRatio;
		chart.ctx.scale(pixelRatio, pixelRatio);

		// If no style has been set on the canvas, the render size is used as display size,
		// making the chart visually bigger, so let's enforce it to the "correct" values.
		// See https://github.com/chartjs/Chart.js/issues/3575
		if (!canvas.style.height && !canvas.style.width) {
			canvas.style.height = height + 'px';
			canvas.style.width = width + 'px';
		}
	};
	// -- Canvas methods
	helpers$1.fontString = function(pixelSize, fontStyle, fontFamily) {
		return fontStyle + ' ' + pixelSize + 'px ' + fontFamily;
	};
	helpers$1.longestText = function(ctx, font, arrayOfThings, cache) {
		cache = cache || {};
		var data = cache.data = cache.data || {};
		var gc = cache.garbageCollect = cache.garbageCollect || [];

		if (cache.font !== font) {
			data = cache.data = {};
			gc = cache.garbageCollect = [];
			cache.font = font;
		}

		ctx.font = font;
		var longest = 0;
		var ilen = arrayOfThings.length;
		var i, j, jlen, thing, nestedThing;
		for (i = 0; i < ilen; i++) {
			thing = arrayOfThings[i];

			// Undefined strings and arrays should not be measured
			if (thing !== undefined && thing !== null && helpers$1.isArray(thing) !== true) {
				longest = helpers$1.measureText(ctx, data, gc, longest, thing);
			} else if (helpers$1.isArray(thing)) {
				// if it is an array lets measure each element
				// to do maybe simplify this function a bit so we can do this more recursively?
				for (j = 0, jlen = thing.length; j < jlen; j++) {
					nestedThing = thing[j];
					// Undefined strings and arrays should not be measured
					if (nestedThing !== undefined && nestedThing !== null && !helpers$1.isArray(nestedThing)) {
						longest = helpers$1.measureText(ctx, data, gc, longest, nestedThing);
					}
				}
			}
		}

		var gcLen = gc.length / 2;
		if (gcLen > arrayOfThings.length) {
			for (i = 0; i < gcLen; i++) {
				delete data[gc[i]];
			}
			gc.splice(0, gcLen);
		}
		return longest;
	};
	helpers$1.measureText = function(ctx, data, gc, longest, string) {
		var textWidth = data[string];
		if (!textWidth) {
			textWidth = data[string] = ctx.measureText(string).width;
			gc.push(string);
		}
		if (textWidth > longest) {
			longest = textWidth;
		}
		return longest;
	};

	/**
	 * @deprecated
	 */
	helpers$1.numberOfLabelLines = function(arrayOfThings) {
		var numberOfLines = 1;
		helpers$1.each(arrayOfThings, function(thing) {
			if (helpers$1.isArray(thing)) {
				if (thing.length > numberOfLines) {
					numberOfLines = thing.length;
				}
			}
		});
		return numberOfLines;
	};

	helpers$1.color = !chartjsColor ?
		function(value) {
			console.error('Color.js not found!');
			return value;
		} :
		function(value) {
			/* global CanvasGradient */
			if (value instanceof CanvasGradient) {
				value = core_defaults.global.defaultColor;
			}

			return chartjsColor(value);
		};

	helpers$1.getHoverColor = function(colorValue) {
		/* global CanvasPattern */
		return (colorValue instanceof CanvasPattern || colorValue instanceof CanvasGradient) ?
			colorValue :
			helpers$1.color(colorValue).saturate(0.5).darken(0.1).rgbString();
	};
};

function abstract() {
	throw new Error(
		'This method is not implemented: either no adapter can ' +
		'be found or an incomplete integration was provided.'
	);
}

/**
 * Date adapter (current used by the time scale)
 * @namespace Chart._adapters._date
 * @memberof Chart._adapters
 * @private
 */

/**
 * Currently supported unit string values.
 * @typedef {('millisecond'|'second'|'minute'|'hour'|'day'|'week'|'month'|'quarter'|'year')}
 * @memberof Chart._adapters._date
 * @name Unit
 */

/**
 * @class
 */
function DateAdapter(options) {
	this.options = options || {};
}

helpers$1.extend(DateAdapter.prototype, /** @lends DateAdapter */ {
	/**
	 * Returns a map of time formats for the supported formatting units defined
	 * in Unit as well as 'datetime' representing a detailed date/time string.
	 * @returns {{string: string}}
	 */
	formats: abstract,

	/**
	 * Parses the given `value` and return the associated timestamp.
	 * @param {any} value - the value to parse (usually comes from the data)
	 * @param {string} [format] - the expected data format
	 * @returns {(number|null)}
	 * @function
	 */
	parse: abstract,

	/**
	 * Returns the formatted date in the specified `format` for a given `timestamp`.
	 * @param {number} timestamp - the timestamp to format
	 * @param {string} format - the date/time token
	 * @return {string}
	 * @function
	 */
	format: abstract,

	/**
	 * Adds the specified `amount` of `unit` to the given `timestamp`.
	 * @param {number} timestamp - the input timestamp
	 * @param {number} amount - the amount to add
	 * @param {Unit} unit - the unit as string
	 * @return {number}
	 * @function
	 */
	add: abstract,

	/**
	 * Returns the number of `unit` between the given timestamps.
	 * @param {number} max - the input timestamp (reference)
	 * @param {number} min - the timestamp to substract
	 * @param {Unit} unit - the unit as string
	 * @return {number}
	 * @function
	 */
	diff: abstract,

	/**
	 * Returns start of `unit` for the given `timestamp`.
	 * @param {number} timestamp - the input timestamp
	 * @param {Unit} unit - the unit as string
	 * @param {number} [weekday] - the ISO day of the week with 1 being Monday
	 * and 7 being Sunday (only needed if param *unit* is `isoWeek`).
	 * @function
	 */
	startOf: abstract,

	/**
	 * Returns end of `unit` for the given `timestamp`.
	 * @param {number} timestamp - the input timestamp
	 * @param {Unit} unit - the unit as string
	 * @function
	 */
	endOf: abstract,

	// DEPRECATIONS

	/**
	 * Provided for backward compatibility for scale.getValueForPixel(),
	 * this method should be overridden only by the moment adapter.
	 * @deprecated since version 2.8.0
	 * @todo remove at version 3
	 * @private
	 */
	_create: function(value) {
		return value;
	}
});

DateAdapter.override = function(members) {
	helpers$1.extend(DateAdapter.prototype, members);
};

var _date = DateAdapter;

var core_adapters = {
	_date: _date
};

/**
 * Namespace to hold static tick generation functions
 * @namespace Chart.Ticks
 */
var core_ticks = {
	/**
	 * Namespace to hold formatters for different types of ticks
	 * @namespace Chart.Ticks.formatters
	 */
	formatters: {
		/**
		 * Formatter for value labels
		 * @method Chart.Ticks.formatters.values
		 * @param value the value to display
		 * @return {string|string[]} the label to display
		 */
		values: function(value) {
			return helpers$1.isArray(value) ? value : '' + value;
		},

		/**
		 * Formatter for linear numeric ticks
		 * @method Chart.Ticks.formatters.linear
		 * @param tickValue {number} the value to be formatted
		 * @param index {number} the position of the tickValue parameter in the ticks array
		 * @param ticks {number[]} the list of ticks being converted
		 * @return {string} string representation of the tickValue parameter
		 */
		linear: function(tickValue, index, ticks) {
			// If we have lots of ticks, don't use the ones
			var delta = ticks.length > 3 ? ticks[2] - ticks[1] : ticks[1] - ticks[0];

			// If we have a number like 2.5 as the delta, figure out how many decimal places we need
			if (Math.abs(delta) > 1) {
				if (tickValue !== Math.floor(tickValue)) {
					// not an integer
					delta = tickValue - Math.floor(tickValue);
				}
			}

			var logDelta = helpers$1.log10(Math.abs(delta));
			var tickString = '';

			if (tickValue !== 0) {
				var maxTick = Math.max(Math.abs(ticks[0]), Math.abs(ticks[ticks.length - 1]));
				if (maxTick < 1e-4) { // all ticks are small numbers; use scientific notation
					var logTick = helpers$1.log10(Math.abs(tickValue));
					var numExponential = Math.floor(logTick) - Math.floor(logDelta);
					numExponential = Math.max(Math.min(numExponential, 20), 0);
					tickString = tickValue.toExponential(numExponential);
				} else {
					var numDecimal = -1 * Math.floor(logDelta);
					numDecimal = Math.max(Math.min(numDecimal, 20), 0); // toFixed has a max of 20 decimal places
					tickString = tickValue.toFixed(numDecimal);
				}
			} else {
				tickString = '0'; // never show decimal places for 0
			}

			return tickString;
		},

		logarithmic: function(tickValue, index, ticks) {
			var remain = tickValue / (Math.pow(10, Math.floor(helpers$1.log10(tickValue))));

			if (tickValue === 0) {
				return '0';
			} else if (remain === 1 || remain === 2 || remain === 5 || index === 0 || index === ticks.length - 1) {
				return tickValue.toExponential();
			}
			return '';
		}
	}
};

var isArray = helpers$1.isArray;
var isNullOrUndef = helpers$1.isNullOrUndef;
var valueOrDefault$a = helpers$1.valueOrDefault;
var valueAtIndexOrDefault = helpers$1.valueAtIndexOrDefault;

core_defaults._set('scale', {
	display: true,
	position: 'left',
	offset: false,

	// grid line settings
	gridLines: {
		display: true,
		color: 'rgba(0,0,0,0.1)',
		lineWidth: 1,
		drawBorder: true,
		drawOnChartArea: true,
		drawTicks: true,
		tickMarkLength: 10,
		zeroLineWidth: 1,
		zeroLineColor: 'rgba(0,0,0,0.25)',
		zeroLineBorderDash: [],
		zeroLineBorderDashOffset: 0.0,
		offsetGridLines: false,
		borderDash: [],
		borderDashOffset: 0.0
	},

	// scale label
	scaleLabel: {
		// display property
		display: false,

		// actual label
		labelString: '',

		// top/bottom padding
		padding: {
			top: 4,
			bottom: 4
		}
	},

	// label settings
	ticks: {
		beginAtZero: false,
		minRotation: 0,
		maxRotation: 50,
		mirror: false,
		padding: 0,
		reverse: false,
		display: true,
		autoSkip: true,
		autoSkipPadding: 0,
		labelOffset: 0,
		// We pass through arrays to be rendered as multiline labels, we convert Others to strings here.
		callback: core_ticks.formatters.values,
		minor: {},
		major: {}
	}
});

/** Returns a new array containing numItems from arr */
function sample(arr, numItems) {
	var result = [];
	var increment = arr.length / numItems;
	var i = 0;
	var len = arr.length;

	for (; i < len; i += increment) {
		result.push(arr[Math.floor(i)]);
	}
	return result;
}

function getPixelForGridLine(scale, index, offsetGridLines) {
	var length = scale.getTicks().length;
	var validIndex = Math.min(index, length - 1);
	var lineValue = scale.getPixelForTick(validIndex);
	var start = scale._startPixel;
	var end = scale._endPixel;
	var epsilon = 1e-6; // 1e-6 is margin in pixels for accumulated error.
	var offset;

	if (offsetGridLines) {
		if (length === 1) {
			offset = Math.max(lineValue - start, end - lineValue);
		} else if (index === 0) {
			offset = (scale.getPixelForTick(1) - lineValue) / 2;
		} else {
			offset = (lineValue - scale.getPixelForTick(validIndex - 1)) / 2;
		}
		lineValue += validIndex < index ? offset : -offset;

		// Return undefined if the pixel is out of the range
		if (lineValue < start - epsilon || lineValue > end + epsilon) {
			return;
		}
	}
	return lineValue;
}

function garbageCollect(caches, length) {
	helpers$1.each(caches, function(cache) {
		var gc = cache.gc;
		var gcLen = gc.length / 2;
		var i;
		if (gcLen > length) {
			for (i = 0; i < gcLen; ++i) {
				delete cache.data[gc[i]];
			}
			gc.splice(0, gcLen);
		}
	});
}

/**
 * Returns {width, height, offset} objects for the first, last, widest, highest tick
 * labels where offset indicates the anchor point offset from the top in pixels.
 */
function computeLabelSizes(ctx, tickFonts, ticks, caches) {
	var length = ticks.length;
	var widths = [];
	var heights = [];
	var offsets = [];
	var widestLabelSize = 0;
	var highestLabelSize = 0;
	var i, j, jlen, label, tickFont, fontString, cache, lineHeight, width, height, nestedLabel, widest, highest;

	for (i = 0; i < length; ++i) {
		label = ticks[i].label;
		tickFont = ticks[i].major ? tickFonts.major : tickFonts.minor;
		ctx.font = fontString = tickFont.string;
		cache = caches[fontString] = caches[fontString] || {data: {}, gc: []};
		lineHeight = tickFont.lineHeight;
		width = height = 0;
		// Undefined labels and arrays should not be measured
		if (!isNullOrUndef(label) && !isArray(label)) {
			width = helpers$1.measureText(ctx, cache.data, cache.gc, width, label);
			height = lineHeight;
		} else if (isArray(label)) {
			// if it is an array let's measure each element
			for (j = 0, jlen = label.length; j < jlen; ++j) {
				nestedLabel = label[j];
				// Undefined labels and arrays should not be measured
				if (!isNullOrUndef(nestedLabel) && !isArray(nestedLabel)) {
					width = helpers$1.measureText(ctx, cache.data, cache.gc, width, nestedLabel);
					height += lineHeight;
				}
			}
		}
		widths.push(width);
		heights.push(height);
		offsets.push(lineHeight / 2);
		widestLabelSize = Math.max(width, widestLabelSize);
		highestLabelSize = Math.max(height, highestLabelSize);
	}
	garbageCollect(caches, length);

	widest = widths.indexOf(widestLabelSize);
	highest = heights.indexOf(highestLabelSize);

	function valueAt(idx) {
		return {
			width: widths[idx] || 0,
			height: heights[idx] || 0,
			offset: offsets[idx] || 0
		};
	}

	return {
		first: valueAt(0),
		last: valueAt(length - 1),
		widest: valueAt(widest),
		highest: valueAt(highest)
	};
}

function getTickMarkLength(options) {
	return options.drawTicks ? options.tickMarkLength : 0;
}

function getScaleLabelHeight(options) {
	var font, padding;

	if (!options.display) {
		return 0;
	}

	font = helpers$1.options._parseFont(options);
	padding = helpers$1.options.toPadding(options.padding);

	return font.lineHeight + padding.height;
}

function parseFontOptions(options, nestedOpts) {
	return helpers$1.extend(helpers$1.options._parseFont({
		fontFamily: valueOrDefault$a(nestedOpts.fontFamily, options.fontFamily),
		fontSize: valueOrDefault$a(nestedOpts.fontSize, options.fontSize),
		fontStyle: valueOrDefault$a(nestedOpts.fontStyle, options.fontStyle),
		lineHeight: valueOrDefault$a(nestedOpts.lineHeight, options.lineHeight)
	}), {
		color: helpers$1.options.resolve([nestedOpts.fontColor, options.fontColor, core_defaults.global.defaultFontColor])
	});
}

function parseTickFontOptions(options) {
	var minor = parseFontOptions(options, options.minor);
	var major = options.major.enabled ? parseFontOptions(options, options.major) : minor;

	return {minor: minor, major: major};
}

function nonSkipped(ticksToFilter) {
	var filtered = [];
	var item, index, len;
	for (index = 0, len = ticksToFilter.length; index < len; ++index) {
		item = ticksToFilter[index];
		if (typeof item._index !== 'undefined') {
			filtered.push(item);
		}
	}
	return filtered;
}

function getEvenSpacing(arr) {
	var len = arr.length;
	var i, diff;

	if (len < 2) {
		return false;
	}

	for (diff = arr[0], i = 1; i < len; ++i) {
		if (arr[i] - arr[i - 1] !== diff) {
			return false;
		}
	}
	return diff;
}

function calculateSpacing(majorIndices, ticks, axisLength, ticksLimit) {
	var evenMajorSpacing = getEvenSpacing(majorIndices);
	var spacing = (ticks.length - 1) / ticksLimit;
	var factors, factor, i, ilen;

	// If the major ticks are evenly spaced apart, place the minor ticks
	// so that they divide the major ticks into even chunks
	if (!evenMajorSpacing) {
		return Math.max(spacing, 1);
	}

	factors = helpers$1.math._factorize(evenMajorSpacing);
	for (i = 0, ilen = factors.length - 1; i < ilen; i++) {
		factor = factors[i];
		if (factor > spacing) {
			return factor;
		}
	}
	return Math.max(spacing, 1);
}

function getMajorIndices(ticks) {
	var result = [];
	var i, ilen;
	for (i = 0, ilen = ticks.length; i < ilen; i++) {
		if (ticks[i].major) {
			result.push(i);
		}
	}
	return result;
}

function skipMajors(ticks, majorIndices, spacing) {
	var count = 0;
	var next = majorIndices[0];
	var i, tick;

	spacing = Math.ceil(spacing);
	for (i = 0; i < ticks.length; i++) {
		tick = ticks[i];
		if (i === next) {
			tick._index = i;
			count++;
			next = majorIndices[count * spacing];
		} else {
			delete tick.label;
		}
	}
}

function skip(ticks, spacing, majorStart, majorEnd) {
	var start = valueOrDefault$a(majorStart, 0);
	var end = Math.min(valueOrDefault$a(majorEnd, ticks.length), ticks.length);
	var count = 0;
	var length, i, tick, next;

	spacing = Math.ceil(spacing);
	if (majorEnd) {
		length = majorEnd - majorStart;
		spacing = length / Math.floor(length / spacing);
	}

	next = start;

	while (next < 0) {
		count++;
		next = Math.round(start + count * spacing);
	}

	for (i = Math.max(start, 0); i < end; i++) {
		tick = ticks[i];
		if (i === next) {
			tick._index = i;
			count++;
			next = Math.round(start + count * spacing);
		} else {
			delete tick.label;
		}
	}
}

var Scale = core_element.extend({

	zeroLineIndex: 0,

	/**
	 * Get the padding needed for the scale
	 * @method getPadding
	 * @private
	 * @returns {Padding} the necessary padding
	 */
	getPadding: function() {
		var me = this;
		return {
			left: me.paddingLeft || 0,
			top: me.paddingTop || 0,
			right: me.paddingRight || 0,
			bottom: me.paddingBottom || 0
		};
	},

	/**
	 * Returns the scale tick objects ({label, major})
	 * @since 2.7
	 */
	getTicks: function() {
		return this._ticks;
	},

	/**
	* @private
	*/
	_getLabels: function() {
		var data = this.chart.data;
		return this.options.labels || (this.isHorizontal() ? data.xLabels : data.yLabels) || data.labels || [];
	},

	// These methods are ordered by lifecyle. Utilities then follow.
	// Any function defined here is inherited by all scale types.
	// Any function can be extended by the scale type

	/**
	 * Provided for backward compatibility, not available anymore
	 * @function Chart.Scale.mergeTicksOptions
	 * @deprecated since version 2.8.0
	 * @todo remove at version 3
	 */
	mergeTicksOptions: function() {
		// noop
	},

	beforeUpdate: function() {
		helpers$1.callback(this.options.beforeUpdate, [this]);
	},

	/**
	 * @param {number} maxWidth - the max width in pixels
	 * @param {number} maxHeight - the max height in pixels
	 * @param {object} margins - the space between the edge of the other scales and edge of the chart
	 *   This space comes from two sources:
	 *     - padding - space that's required to show the labels at the edges of the scale
	 *     - thickness of scales or legends in another orientation
	 */
	update: function(maxWidth, maxHeight, margins) {
		var me = this;
		var tickOpts = me.options.ticks;
		var sampleSize = tickOpts.sampleSize;
		var i, ilen, labels, ticks, samplingEnabled;

		// Update Lifecycle - Probably don't want to ever extend or overwrite this function ;)
		me.beforeUpdate();

		// Absorb the master measurements
		me.maxWidth = maxWidth;
		me.maxHeight = maxHeight;
		me.margins = helpers$1.extend({
			left: 0,
			right: 0,
			top: 0,
			bottom: 0
		}, margins);

		me._ticks = null;
		me.ticks = null;
		me._labelSizes = null;
		me._maxLabelLines = 0;
		me.longestLabelWidth = 0;
		me.longestTextCache = me.longestTextCache || {};
		me._gridLineItems = null;
		me._labelItems = null;

		// Dimensions
		me.beforeSetDimensions();
		me.setDimensions();
		me.afterSetDimensions();

		// Data min/max
		me.beforeDataLimits();
		me.determineDataLimits();
		me.afterDataLimits();

		// Ticks - `this.ticks` is now DEPRECATED!
		// Internal ticks are now stored as objects in the PRIVATE `this._ticks` member
		// and must not be accessed directly from outside this class. `this.ticks` being
		// around for long time and not marked as private, we can't change its structure
		// without unexpected breaking changes. If you need to access the scale ticks,
		// use scale.getTicks() instead.

		me.beforeBuildTicks();

		// New implementations should return an array of objects but for BACKWARD COMPAT,
		// we still support no return (`this.ticks` internally set by calling this method).
		ticks = me.buildTicks() || [];

		// Allow modification of ticks in callback.
		ticks = me.afterBuildTicks(ticks) || ticks;

		// Ensure ticks contains ticks in new tick format
		if ((!ticks || !ticks.length) && me.ticks) {
			ticks = [];
			for (i = 0, ilen = me.ticks.length; i < ilen; ++i) {
				ticks.push({
					value: me.ticks[i],
					major: false
				});
			}
		}

		me._ticks = ticks;

		// Compute tick rotation and fit using a sampled subset of labels
		// We generally don't need to compute the size of every single label for determining scale size
		samplingEnabled = sampleSize < ticks.length;
		labels = me._convertTicksToLabels(samplingEnabled ? sample(ticks, sampleSize) : ticks);

		// _configure is called twice, once here, once from core.controller.updateLayout.
		// Here we haven't been positioned yet, but dimensions are correct.
		// Variables set in _configure are needed for calculateTickRotation, and
		// it's ok that coordinates are not correct there, only dimensions matter.
		me._configure();

		// Tick Rotation
		me.beforeCalculateTickRotation();
		me.calculateTickRotation();
		me.afterCalculateTickRotation();

		me.beforeFit();
		me.fit();
		me.afterFit();

		// Auto-skip
		me._ticksToDraw = tickOpts.display && (tickOpts.autoSkip || tickOpts.source === 'auto') ? me._autoSkip(ticks) : ticks;

		if (samplingEnabled) {
			// Generate labels using all non-skipped ticks
			labels = me._convertTicksToLabels(me._ticksToDraw);
		}

		me.ticks = labels;   // BACKWARD COMPATIBILITY

		// IMPORTANT: after this point, we consider that `this.ticks` will NEVER change!

		me.afterUpdate();

		// TODO(v3): remove minSize as a public property and return value from all layout boxes. It is unused
		// make maxWidth and maxHeight private
		return me.minSize;
	},

	/**
	 * @private
	 */
	_configure: function() {
		var me = this;
		var reversePixels = me.options.ticks.reverse;
		var startPixel, endPixel;

		if (me.isHorizontal()) {
			startPixel = me.left;
			endPixel = me.right;
		} else {
			startPixel = me.top;
			endPixel = me.bottom;
			// by default vertical scales are from bottom to top, so pixels are reversed
			reversePixels = !reversePixels;
		}
		me._startPixel = startPixel;
		me._endPixel = endPixel;
		me._reversePixels = reversePixels;
		me._length = endPixel - startPixel;
	},

	afterUpdate: function() {
		helpers$1.callback(this.options.afterUpdate, [this]);
	},

	//

	beforeSetDimensions: function() {
		helpers$1.callback(this.options.beforeSetDimensions, [this]);
	},
	setDimensions: function() {
		var me = this;
		// Set the unconstrained dimension before label rotation
		if (me.isHorizontal()) {
			// Reset position before calculating rotation
			me.width = me.maxWidth;
			me.left = 0;
			me.right = me.width;
		} else {
			me.height = me.maxHeight;

			// Reset position before calculating rotation
			me.top = 0;
			me.bottom = me.height;
		}

		// Reset padding
		me.paddingLeft = 0;
		me.paddingTop = 0;
		me.paddingRight = 0;
		me.paddingBottom = 0;
	},
	afterSetDimensions: function() {
		helpers$1.callback(this.options.afterSetDimensions, [this]);
	},

	// Data limits
	beforeDataLimits: function() {
		helpers$1.callback(this.options.beforeDataLimits, [this]);
	},
	determineDataLimits: helpers$1.noop,
	afterDataLimits: function() {
		helpers$1.callback(this.options.afterDataLimits, [this]);
	},

	//
	beforeBuildTicks: function() {
		helpers$1.callback(this.options.beforeBuildTicks, [this]);
	},
	buildTicks: helpers$1.noop,
	afterBuildTicks: function(ticks) {
		var me = this;
		// ticks is empty for old axis implementations here
		if (isArray(ticks) && ticks.length) {
			return helpers$1.callback(me.options.afterBuildTicks, [me, ticks]);
		}
		// Support old implementations (that modified `this.ticks` directly in buildTicks)
		me.ticks = helpers$1.callback(me.options.afterBuildTicks, [me, me.ticks]) || me.ticks;
		return ticks;
	},

	beforeTickToLabelConversion: function() {
		helpers$1.callback(this.options.beforeTickToLabelConversion, [this]);
	},
	convertTicksToLabels: function() {
		var me = this;
		// Convert ticks to strings
		var tickOpts = me.options.ticks;
		me.ticks = me.ticks.map(tickOpts.userCallback || tickOpts.callback, this);
	},
	afterTickToLabelConversion: function() {
		helpers$1.callback(this.options.afterTickToLabelConversion, [this]);
	},

	//

	beforeCalculateTickRotation: function() {
		helpers$1.callback(this.options.beforeCalculateTickRotation, [this]);
	},
	calculateTickRotation: function() {
		var me = this;
		var options = me.options;
		var tickOpts = options.ticks;
		var numTicks = me.getTicks().length;
		var minRotation = tickOpts.minRotation || 0;
		var maxRotation = tickOpts.maxRotation;
		var labelRotation = minRotation;
		var labelSizes, maxLabelWidth, maxLabelHeight, maxWidth, tickWidth, maxHeight, maxLabelDiagonal;

		if (!me._isVisible() || !tickOpts.display || minRotation >= maxRotation || numTicks <= 1 || !me.isHorizontal()) {
			me.labelRotation = minRotation;
			return;
		}

		labelSizes = me._getLabelSizes();
		maxLabelWidth = labelSizes.widest.width;
		maxLabelHeight = labelSizes.highest.height - labelSizes.highest.offset;

		// Estimate the width of each grid based on the canvas width, the maximum
		// label width and the number of tick intervals
		maxWidth = Math.min(me.maxWidth, me.chart.width - maxLabelWidth);
		tickWidth = options.offset ? me.maxWidth / numTicks : maxWidth / (numTicks - 1);

		// Allow 3 pixels x2 padding either side for label readability
		if (maxLabelWidth + 6 > tickWidth) {
			tickWidth = maxWidth / (numTicks - (options.offset ? 0.5 : 1));
			maxHeight = me.maxHeight - getTickMarkLength(options.gridLines)
				- tickOpts.padding - getScaleLabelHeight(options.scaleLabel);
			maxLabelDiagonal = Math.sqrt(maxLabelWidth * maxLabelWidth + maxLabelHeight * maxLabelHeight);
			labelRotation = helpers$1.toDegrees(Math.min(
				Math.asin(Math.min((labelSizes.highest.height + 6) / tickWidth, 1)),
				Math.asin(Math.min(maxHeight / maxLabelDiagonal, 1)) - Math.asin(maxLabelHeight / maxLabelDiagonal)
			));
			labelRotation = Math.max(minRotation, Math.min(maxRotation, labelRotation));
		}

		me.labelRotation = labelRotation;
	},
	afterCalculateTickRotation: function() {
		helpers$1.callback(this.options.afterCalculateTickRotation, [this]);
	},

	//

	beforeFit: function() {
		helpers$1.callback(this.options.beforeFit, [this]);
	},
	fit: function() {
		var me = this;
		// Reset
		var minSize = me.minSize = {
			width: 0,
			height: 0
		};

		var chart = me.chart;
		var opts = me.options;
		var tickOpts = opts.ticks;
		var scaleLabelOpts = opts.scaleLabel;
		var gridLineOpts = opts.gridLines;
		var display = me._isVisible();
		var isBottom = opts.position === 'bottom';
		var isHorizontal = me.isHorizontal();

		// Width
		if (isHorizontal) {
			minSize.width = me.maxWidth;
		} else if (display) {
			minSize.width = getTickMarkLength(gridLineOpts) + getScaleLabelHeight(scaleLabelOpts);
		}

		// height
		if (!isHorizontal) {
			minSize.height = me.maxHeight; // fill all the height
		} else if (display) {
			minSize.height = getTickMarkLength(gridLineOpts) + getScaleLabelHeight(scaleLabelOpts);
		}

		// Don't bother fitting the ticks if we are not showing the labels
		if (tickOpts.display && display) {
			var tickFonts = parseTickFontOptions(tickOpts);
			var labelSizes = me._getLabelSizes();
			var firstLabelSize = labelSizes.first;
			var lastLabelSize = labelSizes.last;
			var widestLabelSize = labelSizes.widest;
			var highestLabelSize = labelSizes.highest;
			var lineSpace = tickFonts.minor.lineHeight * 0.4;
			var tickPadding = tickOpts.padding;

			if (isHorizontal) {
				// A horizontal axis is more constrained by the height.
				var isRotated = me.labelRotation !== 0;
				var angleRadians = helpers$1.toRadians(me.labelRotation);
				var cosRotation = Math.cos(angleRadians);
				var sinRotation = Math.sin(angleRadians);

				var labelHeight = sinRotation * widestLabelSize.width
					+ cosRotation * (highestLabelSize.height - (isRotated ? highestLabelSize.offset : 0))
					+ (isRotated ? 0 : lineSpace); // padding

				minSize.height = Math.min(me.maxHeight, minSize.height + labelHeight + tickPadding);

				var offsetLeft = me.getPixelForTick(0) - me.left;
				var offsetRight = me.right - me.getPixelForTick(me.getTicks().length - 1);
				var paddingLeft, paddingRight;

				// Ensure that our ticks are always inside the canvas. When rotated, ticks are right aligned
				// which means that the right padding is dominated by the font height
				if (isRotated) {
					paddingLeft = isBottom ?
						cosRotation * firstLabelSize.width + sinRotation * firstLabelSize.offset :
						sinRotation * (firstLabelSize.height - firstLabelSize.offset);
					paddingRight = isBottom ?
						sinRotation * (lastLabelSize.height - lastLabelSize.offset) :
						cosRotation * lastLabelSize.width + sinRotation * lastLabelSize.offset;
				} else {
					paddingLeft = firstLabelSize.width / 2;
					paddingRight = lastLabelSize.width / 2;
				}

				// Adjust padding taking into account changes in offsets
				// and add 3 px to move away from canvas edges
				me.paddingLeft = Math.max((paddingLeft - offsetLeft) * me.width / (me.width - offsetLeft), 0) + 3;
				me.paddingRight = Math.max((paddingRight - offsetRight) * me.width / (me.width - offsetRight), 0) + 3;
			} else {
				// A vertical axis is more constrained by the width. Labels are the
				// dominant factor here, so get that length first and account for padding
				var labelWidth = tickOpts.mirror ? 0 :
					// use lineSpace for consistency with horizontal axis
					// tickPadding is not implemented for horizontal
					widestLabelSize.width + tickPadding + lineSpace;

				minSize.width = Math.min(me.maxWidth, minSize.width + labelWidth);

				me.paddingTop = firstLabelSize.height / 2;
				me.paddingBottom = lastLabelSize.height / 2;
			}
		}

		me.handleMargins();

		if (isHorizontal) {
			me.width = me._length = chart.width - me.margins.left - me.margins.right;
			me.height = minSize.height;
		} else {
			me.width = minSize.width;
			me.height = me._length = chart.height - me.margins.top - me.margins.bottom;
		}
	},

	/**
	 * Handle margins and padding interactions
	 * @private
	 */
	handleMargins: function() {
		var me = this;
		if (me.margins) {
			me.margins.left = Math.max(me.paddingLeft, me.margins.left);
			me.margins.top = Math.max(me.paddingTop, me.margins.top);
			me.margins.right = Math.max(me.paddingRight, me.margins.right);
			me.margins.bottom = Math.max(me.paddingBottom, me.margins.bottom);
		}
	},

	afterFit: function() {
		helpers$1.callback(this.options.afterFit, [this]);
	},

	// Shared Methods
	isHorizontal: function() {
		var pos = this.options.position;
		return pos === 'top' || pos === 'bottom';
	},
	isFullWidth: function() {
		return this.options.fullWidth;
	},

	// Get the correct value. NaN bad inputs, If the value type is object get the x or y based on whether we are horizontal or not
	getRightValue: function(rawValue) {
		// Null and undefined values first
		if (isNullOrUndef(rawValue)) {
			return NaN;
		}
		// isNaN(object) returns true, so make sure NaN is checking for a number; Discard Infinite values
		if ((typeof rawValue === 'number' || rawValue instanceof Number) && !isFinite(rawValue)) {
			return NaN;
		}

		// If it is in fact an object, dive in one more level
		if (rawValue) {
			if (this.isHorizontal()) {
				if (rawValue.x !== undefined) {
					return this.getRightValue(rawValue.x);
				}
			} else if (rawValue.y !== undefined) {
				return this.getRightValue(rawValue.y);
			}
		}

		// Value is good, return it
		return rawValue;
	},

	_convertTicksToLabels: function(ticks) {
		var me = this;
		var labels, i, ilen;

		me.ticks = ticks.map(function(tick) {
			return tick.value;
		});

		me.beforeTickToLabelConversion();

		// New implementations should return the formatted tick labels but for BACKWARD
		// COMPAT, we still support no return (`this.ticks` internally changed by calling
		// this method and supposed to contain only string values).
		labels = me.convertTicksToLabels(ticks) || me.ticks;

		me.afterTickToLabelConversion();

		// BACKWARD COMPAT: synchronize `_ticks` with labels (so potentially `this.ticks`)
		for (i = 0, ilen = ticks.length; i < ilen; ++i) {
			ticks[i].label = labels[i];
		}

		return labels;
	},

	/**
	 * @private
	 */
	_getLabelSizes: function() {
		var me = this;
		var labelSizes = me._labelSizes;

		if (!labelSizes) {
			me._labelSizes = labelSizes = computeLabelSizes(me.ctx, parseTickFontOptions(me.options.ticks), me.getTicks(), me.longestTextCache);
			me.longestLabelWidth = labelSizes.widest.width;
		}

		return labelSizes;
	},

	/**
	 * @private
	 */
	_parseValue: function(value) {
		var start, end, min, max;

		if (isArray(value)) {
			start = +this.getRightValue(value[0]);
			end = +this.getRightValue(value[1]);
			min = Math.min(start, end);
			max = Math.max(start, end);
		} else {
			value = +this.getRightValue(value);
			start = undefined;
			end = value;
			min = value;
			max = value;
		}

		return {
			min: min,
			max: max,
			start: start,
			end: end
		};
	},

	/**
	* @private
	*/
	_getScaleLabel: function(rawValue) {
		var v = this._parseValue(rawValue);
		if (v.start !== undefined) {
			return '[' + v.start + ', ' + v.end + ']';
		}

		return +this.getRightValue(rawValue);
	},

	/**
	 * Used to get the value to display in the tooltip for the data at the given index
	 * @param index
	 * @param datasetIndex
	 */
	getLabelForIndex: helpers$1.noop,

	/**
	 * Returns the location of the given data point. Value can either be an index or a numerical value
	 * The coordinate (0, 0) is at the upper-left corner of the canvas
	 * @param value
	 * @param index
	 * @param datasetIndex
	 */
	getPixelForValue: helpers$1.noop,

	/**
	 * Used to get the data value from a given pixel. This is the inverse of getPixelForValue
	 * The coordinate (0, 0) is at the upper-left corner of the canvas
	 * @param pixel
	 */
	getValueForPixel: helpers$1.noop,

	/**
	 * Returns the location of the tick at the given index
	 * The coordinate (0, 0) is at the upper-left corner of the canvas
	 */
	getPixelForTick: function(index) {
		var me = this;
		var offset = me.options.offset;
		var numTicks = me._ticks.length;
		var tickWidth = 1 / Math.max(numTicks - (offset ? 0 : 1), 1);

		return index < 0 || index > numTicks - 1
			? null
			: me.getPixelForDecimal(index * tickWidth + (offset ? tickWidth / 2 : 0));
	},

	/**
	 * Utility for getting the pixel location of a percentage of scale
	 * The coordinate (0, 0) is at the upper-left corner of the canvas
	 */
	getPixelForDecimal: function(decimal) {
		var me = this;

		if (me._reversePixels) {
			decimal = 1 - decimal;
		}

		return me._startPixel + decimal * me._length;
	},

	getDecimalForPixel: function(pixel) {
		var decimal = (pixel - this._startPixel) / this._length;
		return this._reversePixels ? 1 - decimal : decimal;
	},

	/**
	 * Returns the pixel for the minimum chart value
	 * The coordinate (0, 0) is at the upper-left corner of the canvas
	 */
	getBasePixel: function() {
		return this.getPixelForValue(this.getBaseValue());
	},

	getBaseValue: function() {
		var me = this;
		var min = me.min;
		var max = me.max;

		return me.beginAtZero ? 0 :
			min < 0 && max < 0 ? max :
			min > 0 && max > 0 ? min :
			0;
	},

	/**
	 * Returns a subset of ticks to be plotted to avoid overlapping labels.
	 * @private
	 */
	_autoSkip: function(ticks) {
		var me = this;
		var tickOpts = me.options.ticks;
		var axisLength = me._length;
		var ticksLimit = tickOpts.maxTicksLimit || axisLength / me._tickSize() + 1;
		var majorIndices = tickOpts.major.enabled ? getMajorIndices(ticks) : [];
		var numMajorIndices = majorIndices.length;
		var first = majorIndices[0];
		var last = majorIndices[numMajorIndices - 1];
		var i, ilen, spacing, avgMajorSpacing;

		// If there are too many major ticks to display them all
		if (numMajorIndices > ticksLimit) {
			skipMajors(ticks, majorIndices, numMajorIndices / ticksLimit);
			return nonSkipped(ticks);
		}

		spacing = calculateSpacing(majorIndices, ticks, axisLength, ticksLimit);

		if (numMajorIndices > 0) {
			for (i = 0, ilen = numMajorIndices - 1; i < ilen; i++) {
				skip(ticks, spacing, majorIndices[i], majorIndices[i + 1]);
			}
			avgMajorSpacing = numMajorIndices > 1 ? (last - first) / (numMajorIndices - 1) : null;
			skip(ticks, spacing, helpers$1.isNullOrUndef(avgMajorSpacing) ? 0 : first - avgMajorSpacing, first);
			skip(ticks, spacing, last, helpers$1.isNullOrUndef(avgMajorSpacing) ? ticks.length : last + avgMajorSpacing);
			return nonSkipped(ticks);
		}
		skip(ticks, spacing);
		return nonSkipped(ticks);
	},

	/**
	 * @private
	 */
	_tickSize: function() {
		var me = this;
		var optionTicks = me.options.ticks;

		// Calculate space needed by label in axis direction.
		var rot = helpers$1.toRadians(me.labelRotation);
		var cos = Math.abs(Math.cos(rot));
		var sin = Math.abs(Math.sin(rot));

		var labelSizes = me._getLabelSizes();
		var padding = optionTicks.autoSkipPadding || 0;
		var w = labelSizes ? labelSizes.widest.width + padding : 0;
		var h = labelSizes ? labelSizes.highest.height + padding : 0;

		// Calculate space needed for 1 tick in axis direction.
		return me.isHorizontal()
			? h * cos > w * sin ? w / cos : h / sin
			: h * sin < w * cos ? h / cos : w / sin;
	},

	/**
	 * @private
	 */
	_isVisible: function() {
		var me = this;
		var chart = me.chart;
		var display = me.options.display;
		var i, ilen, meta;

		if (display !== 'auto') {
			return !!display;
		}

		// When 'auto', the scale is visible if at least one associated dataset is visible.
		for (i = 0, ilen = chart.data.datasets.length; i < ilen; ++i) {
			if (chart.isDatasetVisible(i)) {
				meta = chart.getDatasetMeta(i);
				if (meta.xAxisID === me.id || meta.yAxisID === me.id) {
					return true;
				}
			}
		}

		return false;
	},

	/**
	 * @private
	 */
	_computeGridLineItems: function(chartArea) {
		var me = this;
		var chart = me.chart;
		var options = me.options;
		var gridLines = options.gridLines;
		var position = options.position;
		var offsetGridLines = gridLines.offsetGridLines;
		var isHorizontal = me.isHorizontal();
		var ticks = me._ticksToDraw;
		var ticksLength = ticks.length + (offsetGridLines ? 1 : 0);

		var tl = getTickMarkLength(gridLines);
		var items = [];
		var axisWidth = gridLines.drawBorder ? valueAtIndexOrDefault(gridLines.lineWidth, 0, 0) : 0;
		var axisHalfWidth = axisWidth / 2;
		var alignPixel = helpers$1._alignPixel;
		var alignBorderValue = function(pixel) {
			return alignPixel(chart, pixel, axisWidth);
		};
		var borderValue, i, tick, lineValue, alignedLineValue;
		var tx1, ty1, tx2, ty2, x1, y1, x2, y2, lineWidth, lineColor, borderDash, borderDashOffset;

		if (position === 'top') {
			borderValue = alignBorderValue(me.bottom);
			ty1 = me.bottom - tl;
			ty2 = borderValue - axisHalfWidth;
			y1 = alignBorderValue(chartArea.top) + axisHalfWidth;
			y2 = chartArea.bottom;
		} else if (position === 'bottom') {
			borderValue = alignBorderValue(me.top);
			y1 = chartArea.top;
			y2 = alignBorderValue(chartArea.bottom) - axisHalfWidth;
			ty1 = borderValue + axisHalfWidth;
			ty2 = me.top + tl;
		} else if (position === 'left') {
			borderValue = alignBorderValue(me.right);
			tx1 = me.right - tl;
			tx2 = borderValue - axisHalfWidth;
			x1 = alignBorderValue(chartArea.left) + axisHalfWidth;
			x2 = chartArea.right;
		} else {
			borderValue = alignBorderValue(me.left);
			x1 = chartArea.left;
			x2 = alignBorderValue(chartArea.right) - axisHalfWidth;
			tx1 = borderValue + axisHalfWidth;
			tx2 = me.left + tl;
		}

		for (i = 0; i < ticksLength; ++i) {
			tick = ticks[i] || {};

			// autoskipper skipped this tick (#4635)
			if (isNullOrUndef(tick.label) && i < ticks.length) {
				continue;
			}

			if (i === me.zeroLineIndex && options.offset === offsetGridLines) {
				// Draw the first index specially
				lineWidth = gridLines.zeroLineWidth;
				lineColor = gridLines.zeroLineColor;
				borderDash = gridLines.zeroLineBorderDash || [];
				borderDashOffset = gridLines.zeroLineBorderDashOffset || 0.0;
			} else {
				lineWidth = valueAtIndexOrDefault(gridLines.lineWidth, i, 1);
				lineColor = valueAtIndexOrDefault(gridLines.color, i, 'rgba(0,0,0,0.1)');
				borderDash = gridLines.borderDash || [];
				borderDashOffset = gridLines.borderDashOffset || 0.0;
			}

			lineValue = getPixelForGridLine(me, tick._index || i, offsetGridLines);

			// Skip if the pixel is out of the range
			if (lineValue === undefined) {
				continue;
			}

			alignedLineValue = alignPixel(chart, lineValue, lineWidth);

			if (isHorizontal) {
				tx1 = tx2 = x1 = x2 = alignedLineValue;
			} else {
				ty1 = ty2 = y1 = y2 = alignedLineValue;
			}

			items.push({
				tx1: tx1,
				ty1: ty1,
				tx2: tx2,
				ty2: ty2,
				x1: x1,
				y1: y1,
				x2: x2,
				y2: y2,
				width: lineWidth,
				color: lineColor,
				borderDash: borderDash,
				borderDashOffset: borderDashOffset,
			});
		}

		items.ticksLength = ticksLength;
		items.borderValue = borderValue;

		return items;
	},

	/**
	 * @private
	 */
	_computeLabelItems: function() {
		var me = this;
		var options = me.options;
		var optionTicks = options.ticks;
		var position = options.position;
		var isMirrored = optionTicks.mirror;
		var isHorizontal = me.isHorizontal();
		var ticks = me._ticksToDraw;
		var fonts = parseTickFontOptions(optionTicks);
		var tickPadding = optionTicks.padding;
		var tl = getTickMarkLength(options.gridLines);
		var rotation = -helpers$1.toRadians(me.labelRotation);
		var items = [];
		var i, ilen, tick, label, x, y, textAlign, pixel, font, lineHeight, lineCount, textOffset;

		if (position === 'top') {
			y = me.bottom - tl - tickPadding;
			textAlign = !rotation ? 'center' : 'left';
		} else if (position === 'bottom') {
			y = me.top + tl + tickPadding;
			textAlign = !rotation ? 'center' : 'right';
		} else if (position === 'left') {
			x = me.right - (isMirrored ? 0 : tl) - tickPadding;
			textAlign = isMirrored ? 'left' : 'right';
		} else {
			x = me.left + (isMirrored ? 0 : tl) + tickPadding;
			textAlign = isMirrored ? 'right' : 'left';
		}

		for (i = 0, ilen = ticks.length; i < ilen; ++i) {
			tick = ticks[i];
			label = tick.label;

			// autoskipper skipped this tick (#4635)
			if (isNullOrUndef(label)) {
				continue;
			}

			pixel = me.getPixelForTick(tick._index || i) + optionTicks.labelOffset;
			font = tick.major ? fonts.major : fonts.minor;
			lineHeight = font.lineHeight;
			lineCount = isArray(label) ? label.length : 1;

			if (isHorizontal) {
				x = pixel;
				textOffset = position === 'top'
					? ((!rotation ? 0.5 : 1) - lineCount) * lineHeight
					: (!rotation ? 0.5 : 0) * lineHeight;
			} else {
				y = pixel;
				textOffset = (1 - lineCount) * lineHeight / 2;
			}

			items.push({
				x: x,
				y: y,
				rotation: rotation,
				label: label,
				font: font,
				textOffset: textOffset,
				textAlign: textAlign
			});
		}

		return items;
	},

	/**
	 * @private
	 */
	_drawGrid: function(chartArea) {
		var me = this;
		var gridLines = me.options.gridLines;

		if (!gridLines.display) {
			return;
		}

		var ctx = me.ctx;
		var chart = me.chart;
		var alignPixel = helpers$1._alignPixel;
		var axisWidth = gridLines.drawBorder ? valueAtIndexOrDefault(gridLines.lineWidth, 0, 0) : 0;
		var items = me._gridLineItems || (me._gridLineItems = me._computeGridLineItems(chartArea));
		var width, color, i, ilen, item;

		for (i = 0, ilen = items.length; i < ilen; ++i) {
			item = items[i];
			width = item.width;
			color = item.color;

			if (width && color) {
				ctx.save();
				ctx.lineWidth = width;
				ctx.strokeStyle = color;
				if (ctx.setLineDash) {
					ctx.setLineDash(item.borderDash);
					ctx.lineDashOffset = item.borderDashOffset;
				}

				ctx.beginPath();

				if (gridLines.drawTicks) {
					ctx.moveTo(item.tx1, item.ty1);
					ctx.lineTo(item.tx2, item.ty2);
				}

				if (gridLines.drawOnChartArea) {
					ctx.moveTo(item.x1, item.y1);
					ctx.lineTo(item.x2, item.y2);
				}

				ctx.stroke();
				ctx.restore();
			}
		}

		if (axisWidth) {
			// Draw the line at the edge of the axis
			var firstLineWidth = axisWidth;
			var lastLineWidth = valueAtIndexOrDefault(gridLines.lineWidth, items.ticksLength - 1, 1);
			var borderValue = items.borderValue;
			var x1, x2, y1, y2;

			if (me.isHorizontal()) {
				x1 = alignPixel(chart, me.left, firstLineWidth) - firstLineWidth / 2;
				x2 = alignPixel(chart, me.right, lastLineWidth) + lastLineWidth / 2;
				y1 = y2 = borderValue;
			} else {
				y1 = alignPixel(chart, me.top, firstLineWidth) - firstLineWidth / 2;
				y2 = alignPixel(chart, me.bottom, lastLineWidth) + lastLineWidth / 2;
				x1 = x2 = borderValue;
			}

			ctx.lineWidth = axisWidth;
			ctx.strokeStyle = valueAtIndexOrDefault(gridLines.color, 0);
			ctx.beginPath();
			ctx.moveTo(x1, y1);
			ctx.lineTo(x2, y2);
			ctx.stroke();
		}
	},

	/**
	 * @private
	 */
	_drawLabels: function() {
		var me = this;
		var optionTicks = me.options.ticks;

		if (!optionTicks.display) {
			return;
		}

		var ctx = me.ctx;
		var items = me._labelItems || (me._labelItems = me._computeLabelItems());
		var i, j, ilen, jlen, item, tickFont, label, y;

		for (i = 0, ilen = items.length; i < ilen; ++i) {
			item = items[i];
			tickFont = item.font;

			// Make sure we draw text in the correct color and font
			ctx.save();
			ctx.translate(item.x, item.y);
			ctx.rotate(item.rotation);
			ctx.font = tickFont.string;
			ctx.fillStyle = tickFont.color;
			ctx.textBaseline = 'middle';
			ctx.textAlign = item.textAlign;

			label = item.label;
			y = item.textOffset;
			if (isArray(label)) {
				for (j = 0, jlen = label.length; j < jlen; ++j) {
					// We just make sure the multiline element is a string here..
					ctx.fillText('' + label[j], 0, y);
					y += tickFont.lineHeight;
				}
			} else {
				ctx.fillText(label, 0, y);
			}
			ctx.restore();
		}
	},

	/**
	 * @private
	 */
	_drawTitle: function() {
		var me = this;
		var ctx = me.ctx;
		var options = me.options;
		var scaleLabel = options.scaleLabel;

		if (!scaleLabel.display) {
			return;
		}

		var scaleLabelFontColor = valueOrDefault$a(scaleLabel.fontColor, core_defaults.global.defaultFontColor);
		var scaleLabelFont = helpers$1.options._parseFont(scaleLabel);
		var scaleLabelPadding = helpers$1.options.toPadding(scaleLabel.padding);
		var halfLineHeight = scaleLabelFont.lineHeight / 2;
		var position = options.position;
		var rotation = 0;
		var scaleLabelX, scaleLabelY;

		if (me.isHorizontal()) {
			scaleLabelX = me.left + me.width / 2; // midpoint of the width
			scaleLabelY = position === 'bottom'
				? me.bottom - halfLineHeight - scaleLabelPadding.bottom
				: me.top + halfLineHeight + scaleLabelPadding.top;
		} else {
			var isLeft = position === 'left';
			scaleLabelX = isLeft
				? me.left + halfLineHeight + scaleLabelPadding.top
				: me.right - halfLineHeight - scaleLabelPadding.top;
			scaleLabelY = me.top + me.height / 2;
			rotation = isLeft ? -0.5 * Math.PI : 0.5 * Math.PI;
		}

		ctx.save();
		ctx.translate(scaleLabelX, scaleLabelY);
		ctx.rotate(rotation);
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillStyle = scaleLabelFontColor; // render in correct colour
		ctx.font = scaleLabelFont.string;
		ctx.fillText(scaleLabel.labelString, 0, 0);
		ctx.restore();
	},

	draw: function(chartArea) {
		var me = this;

		if (!me._isVisible()) {
			return;
		}

		me._drawGrid(chartArea);
		me._drawTitle();
		me._drawLabels();
	},

	/**
	 * @private
	 */
	_layers: function() {
		var me = this;
		var opts = me.options;
		var tz = opts.ticks && opts.ticks.z || 0;
		var gz = opts.gridLines && opts.gridLines.z || 0;

		if (!me._isVisible() || tz === gz || me.draw !== me._draw) {
			// backward compatibility: draw has been overridden by custom scale
			return [{
				z: tz,
				draw: function() {
					me.draw.apply(me, arguments);
				}
			}];
		}

		return [{
			z: gz,
			draw: function() {
				me._drawGrid.apply(me, arguments);
				me._drawTitle.apply(me, arguments);
			}
		}, {
			z: tz,
			draw: function() {
				me._drawLabels.apply(me, arguments);
			}
		}];
	},

	/**
	 * @private
	 */
	_getMatchingVisibleMetas: function(type) {
		var me = this;
		var isHorizontal = me.isHorizontal();
		return me.chart._getSortedVisibleDatasetMetas()
			.filter(function(meta) {
				return (!type || meta.type === type)
					&& (isHorizontal ? meta.xAxisID === me.id : meta.yAxisID === me.id);
			});
	}
});

Scale.prototype._draw = Scale.prototype.draw;

var core_scale = Scale;

var isNullOrUndef$1 = helpers$1.isNullOrUndef;

var defaultConfig = {
	position: 'bottom'
};

var scale_category = core_scale.extend({
	determineDataLimits: function() {
		var me = this;
		var labels = me._getLabels();
		var ticksOpts = me.options.ticks;
		var min = ticksOpts.min;
		var max = ticksOpts.max;
		var minIndex = 0;
		var maxIndex = labels.length - 1;
		var findIndex;

		if (min !== undefined) {
			// user specified min value
			findIndex = labels.indexOf(min);
			if (findIndex >= 0) {
				minIndex = findIndex;
			}
		}

		if (max !== undefined) {
			// user specified max value
			findIndex = labels.indexOf(max);
			if (findIndex >= 0) {
				maxIndex = findIndex;
			}
		}

		me.minIndex = minIndex;
		me.maxIndex = maxIndex;
		me.min = labels[minIndex];
		me.max = labels[maxIndex];
	},

	buildTicks: function() {
		var me = this;
		var labels = me._getLabels();
		var minIndex = me.minIndex;
		var maxIndex = me.maxIndex;

		// If we are viewing some subset of labels, slice the original array
		me.ticks = (minIndex === 0 && maxIndex === labels.length - 1) ? labels : labels.slice(minIndex, maxIndex + 1);
	},

	getLabelForIndex: function(index, datasetIndex) {
		var me = this;
		var chart = me.chart;

		if (chart.getDatasetMeta(datasetIndex).controller._getValueScaleId() === me.id) {
			return me.getRightValue(chart.data.datasets[datasetIndex].data[index]);
		}

		return me._getLabels()[index];
	},

	_configure: function() {
		var me = this;
		var offset = me.options.offset;
		var ticks = me.ticks;

		core_scale.prototype._configure.call(me);

		if (!me.isHorizontal()) {
			// For backward compatibility, vertical category scale reverse is inverted.
			me._reversePixels = !me._reversePixels;
		}

		if (!ticks) {
			return;
		}

		me._startValue = me.minIndex - (offset ? 0.5 : 0);
		me._valueRange = Math.max(ticks.length - (offset ? 0 : 1), 1);
	},

	// Used to get data value locations.  Value can either be an index or a numerical value
	getPixelForValue: function(value, index, datasetIndex) {
		var me = this;
		var valueCategory, labels, idx;

		if (!isNullOrUndef$1(index) && !isNullOrUndef$1(datasetIndex)) {
			value = me.chart.data.datasets[datasetIndex].data[index];
		}

		// If value is a data object, then index is the index in the data array,
		// not the index of the scale. We need to change that.
		if (!isNullOrUndef$1(value)) {
			valueCategory = me.isHorizontal() ? value.x : value.y;
		}
		if (valueCategory !== undefined || (value !== undefined && isNaN(index))) {
			labels = me._getLabels();
			value = helpers$1.valueOrDefault(valueCategory, value);
			idx = labels.indexOf(value);
			index = idx !== -1 ? idx : index;
			if (isNaN(index)) {
				index = value;
			}
		}
		return me.getPixelForDecimal((index - me._startValue) / me._valueRange);
	},

	getPixelForTick: function(index) {
		var ticks = this.ticks;
		return index < 0 || index > ticks.length - 1
			? null
			: this.getPixelForValue(ticks[index], index + this.minIndex);
	},

	getValueForPixel: function(pixel) {
		var me = this;
		var value = Math.round(me._startValue + me.getDecimalForPixel(pixel) * me._valueRange);
		return Math.min(Math.max(value, 0), me.ticks.length - 1);
	},

	getBasePixel: function() {
		return this.bottom;
	}
});

// INTERNAL: static default options, registered in src/index.js
var _defaults = defaultConfig;
scale_category._defaults = _defaults;

var noop = helpers$1.noop;
var isNullOrUndef$2 = helpers$1.isNullOrUndef;

/**
 * Generate a set of linear ticks
 * @param generationOptions the options used to generate the ticks
 * @param dataRange the range of the data
 * @returns {number[]} array of tick values
 */
function generateTicks(generationOptions, dataRange) {
	var ticks = [];
	// To get a "nice" value for the tick spacing, we will use the appropriately named
	// "nice number" algorithm. See https://stackoverflow.com/questions/8506881/nice-label-algorithm-for-charts-with-minimum-ticks
	// for details.

	var MIN_SPACING = 1e-14;
	var stepSize = generationOptions.stepSize;
	var unit = stepSize || 1;
	var maxNumSpaces = generationOptions.maxTicks - 1;
	var min = generationOptions.min;
	var max = generationOptions.max;
	var precision = generationOptions.precision;
	var rmin = dataRange.min;
	var rmax = dataRange.max;
	var spacing = helpers$1.niceNum((rmax - rmin) / maxNumSpaces / unit) * unit;
	var factor, niceMin, niceMax, numSpaces;

	// Beyond MIN_SPACING floating point numbers being to lose precision
	// such that we can't do the math necessary to generate ticks
	if (spacing < MIN_SPACING && isNullOrUndef$2(min) && isNullOrUndef$2(max)) {
		return [rmin, rmax];
	}

	numSpaces = Math.ceil(rmax / spacing) - Math.floor(rmin / spacing);
	if (numSpaces > maxNumSpaces) {
		// If the calculated num of spaces exceeds maxNumSpaces, recalculate it
		spacing = helpers$1.niceNum(numSpaces * spacing / maxNumSpaces / unit) * unit;
	}

	if (stepSize || isNullOrUndef$2(precision)) {
		// If a precision is not specified, calculate factor based on spacing
		factor = Math.pow(10, helpers$1._decimalPlaces(spacing));
	} else {
		// If the user specified a precision, round to that number of decimal places
		factor = Math.pow(10, precision);
		spacing = Math.ceil(spacing * factor) / factor;
	}

	niceMin = Math.floor(rmin / spacing) * spacing;
	niceMax = Math.ceil(rmax / spacing) * spacing;

	// If min, max and stepSize is set and they make an evenly spaced scale use it.
	if (stepSize) {
		// If very close to our whole number, use it.
		if (!isNullOrUndef$2(min) && helpers$1.almostWhole(min / spacing, spacing / 1000)) {
			niceMin = min;
		}
		if (!isNullOrUndef$2(max) && helpers$1.almostWhole(max / spacing, spacing / 1000)) {
			niceMax = max;
		}
	}

	numSpaces = (niceMax - niceMin) / spacing;
	// If very close to our rounded value, use it.
	if (helpers$1.almostEquals(numSpaces, Math.round(numSpaces), spacing / 1000)) {
		numSpaces = Math.round(numSpaces);
	} else {
		numSpaces = Math.ceil(numSpaces);
	}

	niceMin = Math.round(niceMin * factor) / factor;
	niceMax = Math.round(niceMax * factor) / factor;
	ticks.push(isNullOrUndef$2(min) ? niceMin : min);
	for (var j = 1; j < numSpaces; ++j) {
		ticks.push(Math.round((niceMin + j * spacing) * factor) / factor);
	}
	ticks.push(isNullOrUndef$2(max) ? niceMax : max);

	return ticks;
}

var scale_linearbase = core_scale.extend({
	getRightValue: function(value) {
		if (typeof value === 'string') {
			return +value;
		}
		return core_scale.prototype.getRightValue.call(this, value);
	},

	handleTickRangeOptions: function() {
		var me = this;
		var opts = me.options;
		var tickOpts = opts.ticks;

		// If we are forcing it to begin at 0, but 0 will already be rendered on the chart,
		// do nothing since that would make the chart weird. If the user really wants a weird chart
		// axis, they can manually override it
		if (tickOpts.beginAtZero) {
			var minSign = helpers$1.sign(me.min);
			var maxSign = helpers$1.sign(me.max);

			if (minSign < 0 && maxSign < 0) {
				// move the top up to 0
				me.max = 0;
			} else if (minSign > 0 && maxSign > 0) {
				// move the bottom down to 0
				me.min = 0;
			}
		}

		var setMin = tickOpts.min !== undefined || tickOpts.suggestedMin !== undefined;
		var setMax = tickOpts.max !== undefined || tickOpts.suggestedMax !== undefined;

		if (tickOpts.min !== undefined) {
			me.min = tickOpts.min;
		} else if (tickOpts.suggestedMin !== undefined) {
			if (me.min === null) {
				me.min = tickOpts.suggestedMin;
			} else {
				me.min = Math.min(me.min, tickOpts.suggestedMin);
			}
		}

		if (tickOpts.max !== undefined) {
			me.max = tickOpts.max;
		} else if (tickOpts.suggestedMax !== undefined) {
			if (me.max === null) {
				me.max = tickOpts.suggestedMax;
			} else {
				me.max = Math.max(me.max, tickOpts.suggestedMax);
			}
		}

		if (setMin !== setMax) {
			// We set the min or the max but not both.
			// So ensure that our range is good
			// Inverted or 0 length range can happen when
			// ticks.min is set, and no datasets are visible
			if (me.min >= me.max) {
				if (setMin) {
					me.max = me.min + 1;
				} else {
					me.min = me.max - 1;
				}
			}
		}

		if (me.min === me.max) {
			me.max++;

			if (!tickOpts.beginAtZero) {
				me.min--;
			}
		}
	},

	getTickLimit: function() {
		var me = this;
		var tickOpts = me.options.ticks;
		var stepSize = tickOpts.stepSize;
		var maxTicksLimit = tickOpts.maxTicksLimit;
		var maxTicks;

		if (stepSize) {
			maxTicks = Math.ceil(me.max / stepSize) - Math.floor(me.min / stepSize) + 1;
		} else {
			maxTicks = me._computeTickLimit();
			maxTicksLimit = maxTicksLimit || 11;
		}

		if (maxTicksLimit) {
			maxTicks = Math.min(maxTicksLimit, maxTicks);
		}

		return maxTicks;
	},

	_computeTickLimit: function() {
		return Number.POSITIVE_INFINITY;
	},

	handleDirectionalChanges: noop,

	buildTicks: function() {
		var me = this;
		var opts = me.options;
		var tickOpts = opts.ticks;

		// Figure out what the max number of ticks we can support it is based on the size of
		// the axis area. For now, we say that the minimum tick spacing in pixels must be 40
		// We also limit the maximum number of ticks to 11 which gives a nice 10 squares on
		// the graph. Make sure we always have at least 2 ticks
		var maxTicks = me.getTickLimit();
		maxTicks = Math.max(2, maxTicks);

		var numericGeneratorOptions = {
			maxTicks: maxTicks,
			min: tickOpts.min,
			max: tickOpts.max,
			precision: tickOpts.precision,
			stepSize: helpers$1.valueOrDefault(tickOpts.fixedStepSize, tickOpts.stepSize)
		};
		var ticks = me.ticks = generateTicks(numericGeneratorOptions, me);

		me.handleDirectionalChanges();

		// At this point, we need to update our max and min given the tick values since we have expanded the
		// range of the scale
		me.max = helpers$1.max(ticks);
		me.min = helpers$1.min(ticks);

		if (tickOpts.reverse) {
			ticks.reverse();

			me.start = me.max;
			me.end = me.min;
		} else {
			me.start = me.min;
			me.end = me.max;
		}
	},

	convertTicksToLabels: function() {
		var me = this;
		me.ticksAsNumbers = me.ticks.slice();
		me.zeroLineIndex = me.ticks.indexOf(0);

		core_scale.prototype.convertTicksToLabels.call(me);
	},

	_configure: function() {
		var me = this;
		var ticks = me.getTicks();
		var start = me.min;
		var end = me.max;
		var offset;

		core_scale.prototype._configure.call(me);

		if (me.options.offset && ticks.length) {
			offset = (end - start) / Math.max(ticks.length - 1, 1) / 2;
			start -= offset;
			end += offset;
		}
		me._startValue = start;
		me._endValue = end;
		me._valueRange = end - start;
	}
});

var defaultConfig$1 = {
	position: 'left',
	ticks: {
		callback: core_ticks.formatters.linear
	}
};

var DEFAULT_MIN = 0;
var DEFAULT_MAX = 1;

function getOrCreateStack(stacks, stacked, meta) {
	var key = [
		meta.type,
		// we have a separate stack for stack=undefined datasets when the opts.stacked is undefined
		stacked === undefined && meta.stack === undefined ? meta.index : '',
		meta.stack
	].join('.');

	if (stacks[key] === undefined) {
		stacks[key] = {
			pos: [],
			neg: []
		};
	}

	return stacks[key];
}

function stackData(scale, stacks, meta, data) {
	var opts = scale.options;
	var stacked = opts.stacked;
	var stack = getOrCreateStack(stacks, stacked, meta);
	var pos = stack.pos;
	var neg = stack.neg;
	var ilen = data.length;
	var i, value;

	for (i = 0; i < ilen; ++i) {
		value = scale._parseValue(data[i]);
		if (isNaN(value.min) || isNaN(value.max) || meta.data[i].hidden) {
			continue;
		}

		pos[i] = pos[i] || 0;
		neg[i] = neg[i] || 0;

		if (opts.relativePoints) {
			pos[i] = 100;
		} else if (value.min < 0 || value.max < 0) {
			neg[i] += value.min;
		} else {
			pos[i] += value.max;
		}
	}
}

function updateMinMax(scale, meta, data) {
	var ilen = data.length;
	var i, value;

	for (i = 0; i < ilen; ++i) {
		value = scale._parseValue(data[i]);
		if (isNaN(value.min) || isNaN(value.max) || meta.data[i].hidden) {
			continue;
		}

		scale.min = Math.min(scale.min, value.min);
		scale.max = Math.max(scale.max, value.max);
	}
}

var scale_linear = scale_linearbase.extend({
	determineDataLimits: function() {
		var me = this;
		var opts = me.options;
		var chart = me.chart;
		var datasets = chart.data.datasets;
		var metasets = me._getMatchingVisibleMetas();
		var hasStacks = opts.stacked;
		var stacks = {};
		var ilen = metasets.length;
		var i, meta, data, values;

		me.min = Number.POSITIVE_INFINITY;
		me.max = Number.NEGATIVE_INFINITY;

		if (hasStacks === undefined) {
			for (i = 0; !hasStacks && i < ilen; ++i) {
				meta = metasets[i];
				hasStacks = meta.stack !== undefined;
			}
		}

		for (i = 0; i < ilen; ++i) {
			meta = metasets[i];
			data = datasets[meta.index].data;
			if (hasStacks) {
				stackData(me, stacks, meta, data);
			} else {
				updateMinMax(me, meta, data);
			}
		}

		helpers$1.each(stacks, function(stackValues) {
			values = stackValues.pos.concat(stackValues.neg);
			me.min = Math.min(me.min, helpers$1.min(values));
			me.max = Math.max(me.max, helpers$1.max(values));
		});

		me.min = helpers$1.isFinite(me.min) && !isNaN(me.min) ? me.min : DEFAULT_MIN;
		me.max = helpers$1.isFinite(me.max) && !isNaN(me.max) ? me.max : DEFAULT_MAX;

		// Common base implementation to handle ticks.min, ticks.max, ticks.beginAtZero
		me.handleTickRangeOptions();
	},

	// Returns the maximum number of ticks based on the scale dimension
	_computeTickLimit: function() {
		var me = this;
		var tickFont;

		if (me.isHorizontal()) {
			return Math.ceil(me.width / 40);
		}
		tickFont = helpers$1.options._parseFont(me.options.ticks);
		return Math.ceil(me.height / tickFont.lineHeight);
	},

	// Called after the ticks are built. We need
	handleDirectionalChanges: function() {
		if (!this.isHorizontal()) {
			// We are in a vertical orientation. The top value is the highest. So reverse the array
			this.ticks.reverse();
		}
	},

	getLabelForIndex: function(index, datasetIndex) {
		return this._getScaleLabel(this.chart.data.datasets[datasetIndex].data[index]);
	},

	// Utils
	getPixelForValue: function(value) {
		var me = this;
		return me.getPixelForDecimal((+me.getRightValue(value) - me._startValue) / me._valueRange);
	},

	getValueForPixel: function(pixel) {
		return this._startValue + this.getDecimalForPixel(pixel) * this._valueRange;
	},

	getPixelForTick: function(index) {
		var ticks = this.ticksAsNumbers;
		if (index < 0 || index > ticks.length - 1) {
			return null;
		}
		return this.getPixelForValue(ticks[index]);
	}
});

// INTERNAL: static default options, registered in src/index.js
var _defaults$1 = defaultConfig$1;
scale_linear._defaults = _defaults$1;

var valueOrDefault$b = helpers$1.valueOrDefault;
var log10 = helpers$1.math.log10;

/**
 * Generate a set of logarithmic ticks
 * @param generationOptions the options used to generate the ticks
 * @param dataRange the range of the data
 * @returns {number[]} array of tick values
 */
function generateTicks$1(generationOptions, dataRange) {
	var ticks = [];

	var tickVal = valueOrDefault$b(generationOptions.min, Math.pow(10, Math.floor(log10(dataRange.min))));

	var endExp = Math.floor(log10(dataRange.max));
	var endSignificand = Math.ceil(dataRange.max / Math.pow(10, endExp));
	var exp, significand;

	if (tickVal === 0) {
		exp = Math.floor(log10(dataRange.minNotZero));
		significand = Math.floor(dataRange.minNotZero / Math.pow(10, exp));

		ticks.push(tickVal);
		tickVal = significand * Math.pow(10, exp);
	} else {
		exp = Math.floor(log10(tickVal));
		significand = Math.floor(tickVal / Math.pow(10, exp));
	}
	var precision = exp < 0 ? Math.pow(10, Math.abs(exp)) : 1;

	do {
		ticks.push(tickVal);

		++significand;
		if (significand === 10) {
			significand = 1;
			++exp;
			precision = exp >= 0 ? 1 : precision;
		}

		tickVal = Math.round(significand * Math.pow(10, exp) * precision) / precision;
	} while (exp < endExp || (exp === endExp && significand < endSignificand));

	var lastTick = valueOrDefault$b(generationOptions.max, tickVal);
	ticks.push(lastTick);

	return ticks;
}

var defaultConfig$2 = {
	position: 'left',

	// label settings
	ticks: {
		callback: core_ticks.formatters.logarithmic
	}
};

// TODO(v3): change this to positiveOrDefault
function nonNegativeOrDefault(value, defaultValue) {
	return helpers$1.isFinite(value) && value >= 0 ? value : defaultValue;
}

var scale_logarithmic = core_scale.extend({
	determineDataLimits: function() {
		var me = this;
		var opts = me.options;
		var chart = me.chart;
		var datasets = chart.data.datasets;
		var isHorizontal = me.isHorizontal();
		function IDMatches(meta) {
			return isHorizontal ? meta.xAxisID === me.id : meta.yAxisID === me.id;
		}
		var datasetIndex, meta, value, data, i, ilen;

		// Calculate Range
		me.min = Number.POSITIVE_INFINITY;
		me.max = Number.NEGATIVE_INFINITY;
		me.minNotZero = Number.POSITIVE_INFINITY;

		var hasStacks = opts.stacked;
		if (hasStacks === undefined) {
			for (datasetIndex = 0; datasetIndex < datasets.length; datasetIndex++) {
				meta = chart.getDatasetMeta(datasetIndex);
				if (chart.isDatasetVisible(datasetIndex) && IDMatches(meta) &&
					meta.stack !== undefined) {
					hasStacks = true;
					break;
				}
			}
		}

		if (opts.stacked || hasStacks) {
			var valuesPerStack = {};

			for (datasetIndex = 0; datasetIndex < datasets.length; datasetIndex++) {
				meta = chart.getDatasetMeta(datasetIndex);
				var key = [
					meta.type,
					// we have a separate stack for stack=undefined datasets when the opts.stacked is undefined
					((opts.stacked === undefined && meta.stack === undefined) ? datasetIndex : ''),
					meta.stack
				].join('.');

				if (chart.isDatasetVisible(datasetIndex) && IDMatches(meta)) {
					if (valuesPerStack[key] === undefined) {
						valuesPerStack[key] = [];
					}

					data = datasets[datasetIndex].data;
					for (i = 0, ilen = data.length; i < ilen; i++) {
						var values = valuesPerStack[key];
						value = me._parseValue(data[i]);
						// invalid, hidden and negative values are ignored
						if (isNaN(value.min) || isNaN(value.max) || meta.data[i].hidden || value.min < 0 || value.max < 0) {
							continue;
						}
						values[i] = values[i] || 0;
						values[i] += value.max;
					}
				}
			}

			helpers$1.each(valuesPerStack, function(valuesForType) {
				if (valuesForType.length > 0) {
					var minVal = helpers$1.min(valuesForType);
					var maxVal = helpers$1.max(valuesForType);
					me.min = Math.min(me.min, minVal);
					me.max = Math.max(me.max, maxVal);
				}
			});

		} else {
			for (datasetIndex = 0; datasetIndex < datasets.length; datasetIndex++) {
				meta = chart.getDatasetMeta(datasetIndex);
				if (chart.isDatasetVisible(datasetIndex) && IDMatches(meta)) {
					data = datasets[datasetIndex].data;
					for (i = 0, ilen = data.length; i < ilen; i++) {
						value = me._parseValue(data[i]);
						// invalid, hidden and negative values are ignored
						if (isNaN(value.min) || isNaN(value.max) || meta.data[i].hidden || value.min < 0 || value.max < 0) {
							continue;
						}

						me.min = Math.min(value.min, me.min);
						me.max = Math.max(value.max, me.max);

						if (value.min !== 0) {
							me.minNotZero = Math.min(value.min, me.minNotZero);
						}
					}
				}
			}
		}

		me.min = helpers$1.isFinite(me.min) ? me.min : null;
		me.max = helpers$1.isFinite(me.max) ? me.max : null;
		me.minNotZero = helpers$1.isFinite(me.minNotZero) ? me.minNotZero : null;

		// Common base implementation to handle ticks.min, ticks.max
		this.handleTickRangeOptions();
	},

	handleTickRangeOptions: function() {
		var me = this;
		var tickOpts = me.options.ticks;
		var DEFAULT_MIN = 1;
		var DEFAULT_MAX = 10;

		me.min = nonNegativeOrDefault(tickOpts.min, me.min);
		me.max = nonNegativeOrDefault(tickOpts.max, me.max);

		if (me.min === me.max) {
			if (me.min !== 0 && me.min !== null) {
				me.min = Math.pow(10, Math.floor(log10(me.min)) - 1);
				me.max = Math.pow(10, Math.floor(log10(me.max)) + 1);
			} else {
				me.min = DEFAULT_MIN;
				me.max = DEFAULT_MAX;
			}
		}
		if (me.min === null) {
			me.min = Math.pow(10, Math.floor(log10(me.max)) - 1);
		}
		if (me.max === null) {
			me.max = me.min !== 0
				? Math.pow(10, Math.floor(log10(me.min)) + 1)
				: DEFAULT_MAX;
		}
		if (me.minNotZero === null) {
			if (me.min > 0) {
				me.minNotZero = me.min;
			} else if (me.max < 1) {
				me.minNotZero = Math.pow(10, Math.floor(log10(me.max)));
			} else {
				me.minNotZero = DEFAULT_MIN;
			}
		}
	},

	buildTicks: function() {
		var me = this;
		var tickOpts = me.options.ticks;
		var reverse = !me.isHorizontal();

		var generationOptions = {
			min: nonNegativeOrDefault(tickOpts.min),
			max: nonNegativeOrDefault(tickOpts.max)
		};
		var ticks = me.ticks = generateTicks$1(generationOptions, me);

		// At this point, we need to update our max and min given the tick values since we have expanded the
		// range of the scale
		me.max = helpers$1.max(ticks);
		me.min = helpers$1.min(ticks);

		if (tickOpts.reverse) {
			reverse = !reverse;
			me.start = me.max;
			me.end = me.min;
		} else {
			me.start = me.min;
			me.end = me.max;
		}
		if (reverse) {
			ticks.reverse();
		}
	},

	convertTicksToLabels: function() {
		this.tickValues = this.ticks.slice();

		core_scale.prototype.convertTicksToLabels.call(this);
	},

	// Get the correct tooltip label
	getLabelForIndex: function(index, datasetIndex) {
		return this._getScaleLabel(this.chart.data.datasets[datasetIndex].data[index]);
	},

	getPixelForTick: function(index) {
		var ticks = this.tickValues;
		if (index < 0 || index > ticks.length - 1) {
			return null;
		}
		return this.getPixelForValue(ticks[index]);
	},

	/**
	 * Returns the value of the first tick.
	 * @param {number} value - The minimum not zero value.
	 * @return {number} The first tick value.
	 * @private
	 */
	_getFirstTickValue: function(value) {
		var exp = Math.floor(log10(value));
		var significand = Math.floor(value / Math.pow(10, exp));

		return significand * Math.pow(10, exp);
	},

	_configure: function() {
		var me = this;
		var start = me.min;
		var offset = 0;

		core_scale.prototype._configure.call(me);

		if (start === 0) {
			start = me._getFirstTickValue(me.minNotZero);
			offset = valueOrDefault$b(me.options.ticks.fontSize, core_defaults.global.defaultFontSize) / me._length;
		}

		me._startValue = log10(start);
		me._valueOffset = offset;
		me._valueRange = (log10(me.max) - log10(start)) / (1 - offset);
	},

	getPixelForValue: function(value) {
		var me = this;
		var decimal = 0;

		value = +me.getRightValue(value);

		if (value > me.min && value > 0) {
			decimal = (log10(value) - me._startValue) / me._valueRange + me._valueOffset;
		}
		return me.getPixelForDecimal(decimal);
	},

	getValueForPixel: function(pixel) {
		var me = this;
		var decimal = me.getDecimalForPixel(pixel);
		return decimal === 0 && me.min === 0
			? 0
			: Math.pow(10, me._startValue + (decimal - me._valueOffset) * me._valueRange);
	}
});

// INTERNAL: static default options, registered in src/index.js
var _defaults$2 = defaultConfig$2;
scale_logarithmic._defaults = _defaults$2;

var valueOrDefault$c = helpers$1.valueOrDefault;
var valueAtIndexOrDefault$1 = helpers$1.valueAtIndexOrDefault;
var resolve$4 = helpers$1.options.resolve;

var defaultConfig$3 = {
	display: true,

	// Boolean - Whether to animate scaling the chart from the centre
	animate: true,
	position: 'chartArea',

	angleLines: {
		display: true,
		color: 'rgba(0,0,0,0.1)',
		lineWidth: 1,
		borderDash: [],
		borderDashOffset: 0.0
	},

	gridLines: {
		circular: false
	},

	// label settings
	ticks: {
		// Boolean - Show a backdrop to the scale label
		showLabelBackdrop: true,

		// String - The colour of the label backdrop
		backdropColor: 'rgba(255,255,255,0.75)',

		// Number - The backdrop padding above & below the label in pixels
		backdropPaddingY: 2,

		// Number - The backdrop padding to the side of the label in pixels
		backdropPaddingX: 2,

		callback: core_ticks.formatters.linear
	},

	pointLabels: {
		// Boolean - if true, show point labels
		display: true,

		// Number - Point label font size in pixels
		fontSize: 10,

		// Function - Used to convert point labels
		callback: function(label) {
			return label;
		}
	}
};

function getTickBackdropHeight(opts) {
	var tickOpts = opts.ticks;

	if (tickOpts.display && opts.display) {
		return valueOrDefault$c(tickOpts.fontSize, core_defaults.global.defaultFontSize) + tickOpts.backdropPaddingY * 2;
	}
	return 0;
}

function measureLabelSize(ctx, lineHeight, label) {
	if (helpers$1.isArray(label)) {
		return {
			w: helpers$1.longestText(ctx, ctx.font, label),
			h: label.length * lineHeight
		};
	}

	return {
		w: ctx.measureText(label).width,
		h: lineHeight
	};
}

function determineLimits(angle, pos, size, min, max) {
	if (angle === min || angle === max) {
		return {
			start: pos - (size / 2),
			end: pos + (size / 2)
		};
	} else if (angle < min || angle > max) {
		return {
			start: pos - size,
			end: pos
		};
	}

	return {
		start: pos,
		end: pos + size
	};
}

/**
 * Helper function to fit a radial linear scale with point labels
 */
function fitWithPointLabels(scale) {

	// Right, this is really confusing and there is a lot of maths going on here
	// The gist of the problem is here: https://gist.github.com/nnnick/696cc9c55f4b0beb8fe9
	//
	// Reaction: https://dl.dropboxusercontent.com/u/34601363/toomuchscience.gif
	//
	// Solution:
	//
	// We assume the radius of the polygon is half the size of the canvas at first
	// at each index we check if the text overlaps.
	//
	// Where it does, we store that angle and that index.
	//
	// After finding the largest index and angle we calculate how much we need to remove
	// from the shape radius to move the point inwards by that x.
	//
	// We average the left and right distances to get the maximum shape radius that can fit in the box
	// along with labels.
	//
	// Once we have that, we can find the centre point for the chart, by taking the x text protrusion
	// on each side, removing that from the size, halving it and adding the left x protrusion width.
	//
	// This will mean we have a shape fitted to the canvas, as large as it can be with the labels
	// and position it in the most space efficient manner
	//
	// https://dl.dropboxusercontent.com/u/34601363/yeahscience.gif

	var plFont = helpers$1.options._parseFont(scale.options.pointLabels);

	// Get maximum radius of the polygon. Either half the height (minus the text width) or half the width.
	// Use this to calculate the offset + change. - Make sure L/R protrusion is at least 0 to stop issues with centre points
	var furthestLimits = {
		l: 0,
		r: scale.width,
		t: 0,
		b: scale.height - scale.paddingTop
	};
	var furthestAngles = {};
	var i, textSize, pointPosition;

	scale.ctx.font = plFont.string;
	scale._pointLabelSizes = [];

	var valueCount = scale.chart.data.labels.length;
	for (i = 0; i < valueCount; i++) {
		pointPosition = scale.getPointPosition(i, scale.drawingArea + 5);
		textSize = measureLabelSize(scale.ctx, plFont.lineHeight, scale.pointLabels[i]);
		scale._pointLabelSizes[i] = textSize;

		// Add quarter circle to make degree 0 mean top of circle
		var angleRadians = scale.getIndexAngle(i);
		var angle = helpers$1.toDegrees(angleRadians) % 360;
		var hLimits = determineLimits(angle, pointPosition.x, textSize.w, 0, 180);
		var vLimits = determineLimits(angle, pointPosition.y, textSize.h, 90, 270);

		if (hLimits.start < furthestLimits.l) {
			furthestLimits.l = hLimits.start;
			furthestAngles.l = angleRadians;
		}

		if (hLimits.end > furthestLimits.r) {
			furthestLimits.r = hLimits.end;
			furthestAngles.r = angleRadians;
		}

		if (vLimits.start < furthestLimits.t) {
			furthestLimits.t = vLimits.start;
			furthestAngles.t = angleRadians;
		}

		if (vLimits.end > furthestLimits.b) {
			furthestLimits.b = vLimits.end;
			furthestAngles.b = angleRadians;
		}
	}

	scale.setReductions(scale.drawingArea, furthestLimits, furthestAngles);
}

function getTextAlignForAngle(angle) {
	if (angle === 0 || angle === 180) {
		return 'center';
	} else if (angle < 180) {
		return 'left';
	}

	return 'right';
}

function fillText(ctx, text, position, lineHeight) {
	var y = position.y + lineHeight / 2;
	var i, ilen;

	if (helpers$1.isArray(text)) {
		for (i = 0, ilen = text.length; i < ilen; ++i) {
			ctx.fillText(text[i], position.x, y);
			y += lineHeight;
		}
	} else {
		ctx.fillText(text, position.x, y);
	}
}

function adjustPointPositionForLabelHeight(angle, textSize, position) {
	if (angle === 90 || angle === 270) {
		position.y -= (textSize.h / 2);
	} else if (angle > 270 || angle < 90) {
		position.y -= textSize.h;
	}
}

function drawPointLabels(scale) {
	var ctx = scale.ctx;
	var opts = scale.options;
	var pointLabelOpts = opts.pointLabels;
	var tickBackdropHeight = getTickBackdropHeight(opts);
	var outerDistance = scale.getDistanceFromCenterForValue(opts.ticks.reverse ? scale.min : scale.max);
	var plFont = helpers$1.options._parseFont(pointLabelOpts);

	ctx.save();

	ctx.font = plFont.string;
	ctx.textBaseline = 'middle';

	for (var i = scale.chart.data.labels.length - 1; i >= 0; i--) {
		// Extra pixels out for some label spacing
		var extra = (i === 0 ? tickBackdropHeight / 2 : 0);
		var pointLabelPosition = scale.getPointPosition(i, outerDistance + extra + 5);

		// Keep this in loop since we may support array properties here
		var pointLabelFontColor = valueAtIndexOrDefault$1(pointLabelOpts.fontColor, i, core_defaults.global.defaultFontColor);
		ctx.fillStyle = pointLabelFontColor;

		var angleRadians = scale.getIndexAngle(i);
		var angle = helpers$1.toDegrees(angleRadians);
		ctx.textAlign = getTextAlignForAngle(angle);
		adjustPointPositionForLabelHeight(angle, scale._pointLabelSizes[i], pointLabelPosition);
		fillText(ctx, scale.pointLabels[i], pointLabelPosition, plFont.lineHeight);
	}
	ctx.restore();
}

function drawRadiusLine(scale, gridLineOpts, radius, index) {
	var ctx = scale.ctx;
	var circular = gridLineOpts.circular;
	var valueCount = scale.chart.data.labels.length;
	var lineColor = valueAtIndexOrDefault$1(gridLineOpts.color, index - 1);
	var lineWidth = valueAtIndexOrDefault$1(gridLineOpts.lineWidth, index - 1);
	var pointPosition;

	if ((!circular && !valueCount) || !lineColor || !lineWidth) {
		return;
	}

	ctx.save();
	ctx.strokeStyle = lineColor;
	ctx.lineWidth = lineWidth;
	if (ctx.setLineDash) {
		ctx.setLineDash(gridLineOpts.borderDash || []);
		ctx.lineDashOffset = gridLineOpts.borderDashOffset || 0.0;
	}

	ctx.beginPath();
	if (circular) {
		// Draw circular arcs between the points
		ctx.arc(scale.xCenter, scale.yCenter, radius, 0, Math.PI * 2);
	} else {
		// Draw straight lines connecting each index
		pointPosition = scale.getPointPosition(0, radius);
		ctx.moveTo(pointPosition.x, pointPosition.y);

		for (var i = 1; i < valueCount; i++) {
			pointPosition = scale.getPointPosition(i, radius);
			ctx.lineTo(pointPosition.x, pointPosition.y);
		}
	}
	ctx.closePath();
	ctx.stroke();
	ctx.restore();
}

function numberOrZero(param) {
	return helpers$1.isNumber(param) ? param : 0;
}

var scale_radialLinear = scale_linearbase.extend({
	setDimensions: function() {
		var me = this;

		// Set the unconstrained dimension before label rotation
		me.width = me.maxWidth;
		me.height = me.maxHeight;
		me.paddingTop = getTickBackdropHeight(me.options) / 2;
		me.xCenter = Math.floor(me.width / 2);
		me.yCenter = Math.floor((me.height - me.paddingTop) / 2);
		me.drawingArea = Math.min(me.height - me.paddingTop, me.width) / 2;
	},

	determineDataLimits: function() {
		var me = this;
		var chart = me.chart;
		var min = Number.POSITIVE_INFINITY;
		var max = Number.NEGATIVE_INFINITY;

		helpers$1.each(chart.data.datasets, function(dataset, datasetIndex) {
			if (chart.isDatasetVisible(datasetIndex)) {
				var meta = chart.getDatasetMeta(datasetIndex);

				helpers$1.each(dataset.data, function(rawValue, index) {
					var value = +me.getRightValue(rawValue);
					if (isNaN(value) || meta.data[index].hidden) {
						return;
					}

					min = Math.min(value, min);
					max = Math.max(value, max);
				});
			}
		});

		me.min = (min === Number.POSITIVE_INFINITY ? 0 : min);
		me.max = (max === Number.NEGATIVE_INFINITY ? 0 : max);

		// Common base implementation to handle ticks.min, ticks.max, ticks.beginAtZero
		me.handleTickRangeOptions();
	},

	// Returns the maximum number of ticks based on the scale dimension
	_computeTickLimit: function() {
		return Math.ceil(this.drawingArea / getTickBackdropHeight(this.options));
	},

	convertTicksToLabels: function() {
		var me = this;

		scale_linearbase.prototype.convertTicksToLabels.call(me);

		// Point labels
		me.pointLabels = me.chart.data.labels.map(function() {
			var label = helpers$1.callback(me.options.pointLabels.callback, arguments, me);
			return label || label === 0 ? label : '';
		});
	},

	getLabelForIndex: function(index, datasetIndex) {
		return +this.getRightValue(this.chart.data.datasets[datasetIndex].data[index]);
	},

	fit: function() {
		var me = this;
		var opts = me.options;

		if (opts.display && opts.pointLabels.display) {
			fitWithPointLabels(me);
		} else {
			me.setCenterPoint(0, 0, 0, 0);
		}
	},

	/**
	 * Set radius reductions and determine new radius and center point
	 * @private
	 */
	setReductions: function(largestPossibleRadius, furthestLimits, furthestAngles) {
		var me = this;
		var radiusReductionLeft = furthestLimits.l / Math.sin(furthestAngles.l);
		var radiusReductionRight = Math.max(furthestLimits.r - me.width, 0) / Math.sin(furthestAngles.r);
		var radiusReductionTop = -furthestLimits.t / Math.cos(furthestAngles.t);
		var radiusReductionBottom = -Math.max(furthestLimits.b - (me.height - me.paddingTop), 0) / Math.cos(furthestAngles.b);

		radiusReductionLeft = numberOrZero(radiusReductionLeft);
		radiusReductionRight = numberOrZero(radiusReductionRight);
		radiusReductionTop = numberOrZero(radiusReductionTop);
		radiusReductionBottom = numberOrZero(radiusReductionBottom);

		me.drawingArea = Math.min(
			Math.floor(largestPossibleRadius - (radiusReductionLeft + radiusReductionRight) / 2),
			Math.floor(largestPossibleRadius - (radiusReductionTop + radiusReductionBottom) / 2));
		me.setCenterPoint(radiusReductionLeft, radiusReductionRight, radiusReductionTop, radiusReductionBottom);
	},

	setCenterPoint: function(leftMovement, rightMovement, topMovement, bottomMovement) {
		var me = this;
		var maxRight = me.width - rightMovement - me.drawingArea;
		var maxLeft = leftMovement + me.drawingArea;
		var maxTop = topMovement + me.drawingArea;
		var maxBottom = (me.height - me.paddingTop) - bottomMovement - me.drawingArea;

		me.xCenter = Math.floor(((maxLeft + maxRight) / 2) + me.left);
		me.yCenter = Math.floor(((maxTop + maxBottom) / 2) + me.top + me.paddingTop);
	},

	getIndexAngle: function(index) {
		var chart = this.chart;
		var angleMultiplier = 360 / chart.data.labels.length;
		var options = chart.options || {};
		var startAngle = options.startAngle || 0;

		// Start from the top instead of right, so remove a quarter of the circle
		var angle = (index * angleMultiplier + startAngle) % 360;

		return (angle < 0 ? angle + 360 : angle) * Math.PI * 2 / 360;
	},

	getDistanceFromCenterForValue: function(value) {
		var me = this;

		if (helpers$1.isNullOrUndef(value)) {
			return NaN;
		}

		// Take into account half font size + the yPadding of the top value
		var scalingFactor = me.drawingArea / (me.max - me.min);
		if (me.options.ticks.reverse) {
			return (me.max - value) * scalingFactor;
		}
		return (value - me.min) * scalingFactor;
	},

	getPointPosition: function(index, distanceFromCenter) {
		var me = this;
		var thisAngle = me.getIndexAngle(index) - (Math.PI / 2);
		return {
			x: Math.cos(thisAngle) * distanceFromCenter + me.xCenter,
			y: Math.sin(thisAngle) * distanceFromCenter + me.yCenter
		};
	},

	getPointPositionForValue: function(index, value) {
		return this.getPointPosition(index, this.getDistanceFromCenterForValue(value));
	},

	getBasePosition: function(index) {
		var me = this;
		var min = me.min;
		var max = me.max;

		return me.getPointPositionForValue(index || 0,
			me.beginAtZero ? 0 :
			min < 0 && max < 0 ? max :
			min > 0 && max > 0 ? min :
			0);
	},

	/**
	 * @private
	 */
	_drawGrid: function() {
		var me = this;
		var ctx = me.ctx;
		var opts = me.options;
		var gridLineOpts = opts.gridLines;
		var angleLineOpts = opts.angleLines;
		var lineWidth = valueOrDefault$c(angleLineOpts.lineWidth, gridLineOpts.lineWidth);
		var lineColor = valueOrDefault$c(angleLineOpts.color, gridLineOpts.color);
		var i, offset, position;

		if (opts.pointLabels.display) {
			drawPointLabels(me);
		}

		if (gridLineOpts.display) {
			helpers$1.each(me.ticks, function(label, index) {
				if (index !== 0) {
					offset = me.getDistanceFromCenterForValue(me.ticksAsNumbers[index]);
					drawRadiusLine(me, gridLineOpts, offset, index);
				}
			});
		}

		if (angleLineOpts.display && lineWidth && lineColor) {
			ctx.save();
			ctx.lineWidth = lineWidth;
			ctx.strokeStyle = lineColor;
			if (ctx.setLineDash) {
				ctx.setLineDash(resolve$4([angleLineOpts.borderDash, gridLineOpts.borderDash, []]));
				ctx.lineDashOffset = resolve$4([angleLineOpts.borderDashOffset, gridLineOpts.borderDashOffset, 0.0]);
			}

			for (i = me.chart.data.labels.length - 1; i >= 0; i--) {
				offset = me.getDistanceFromCenterForValue(opts.ticks.reverse ? me.min : me.max);
				position = me.getPointPosition(i, offset);
				ctx.beginPath();
				ctx.moveTo(me.xCenter, me.yCenter);
				ctx.lineTo(position.x, position.y);
				ctx.stroke();
			}

			ctx.restore();
		}
	},

	/**
	 * @private
	 */
	_drawLabels: function() {
		var me = this;
		var ctx = me.ctx;
		var opts = me.options;
		var tickOpts = opts.ticks;

		if (!tickOpts.display) {
			return;
		}

		var startAngle = me.getIndexAngle(0);
		var tickFont = helpers$1.options._parseFont(tickOpts);
		var tickFontColor = valueOrDefault$c(tickOpts.fontColor, core_defaults.global.defaultFontColor);
		var offset, width;

		ctx.save();
		ctx.font = tickFont.string;
		ctx.translate(me.xCenter, me.yCenter);
		ctx.rotate(startAngle);
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';

		helpers$1.each(me.ticks, function(label, index) {
			if (index === 0 && !tickOpts.reverse) {
				return;
			}

			offset = me.getDistanceFromCenterForValue(me.ticksAsNumbers[index]);

			if (tickOpts.showLabelBackdrop) {
				width = ctx.measureText(label).width;
				ctx.fillStyle = tickOpts.backdropColor;

				ctx.fillRect(
					-width / 2 - tickOpts.backdropPaddingX,
					-offset - tickFont.size / 2 - tickOpts.backdropPaddingY,
					width + tickOpts.backdropPaddingX * 2,
					tickFont.size + tickOpts.backdropPaddingY * 2
				);
			}

			ctx.fillStyle = tickFontColor;
			ctx.fillText(label, 0, -offset);
		});

		ctx.restore();
	},

	/**
	 * @private
	 */
	_drawTitle: helpers$1.noop
});

// INTERNAL: static default options, registered in src/index.js
var _defaults$3 = defaultConfig$3;
scale_radialLinear._defaults = _defaults$3;

var deprecated$1 = helpers$1._deprecated;
var resolve$5 = helpers$1.options.resolve;
var valueOrDefault$d = helpers$1.valueOrDefault;

// Integer constants are from the ES6 spec.
var MIN_INTEGER = Number.MIN_SAFE_INTEGER || -9007199254740991;
var MAX_INTEGER = Number.MAX_SAFE_INTEGER || 9007199254740991;

var INTERVALS = {
	millisecond: {
		common: true,
		size: 1,
		steps: 1000
	},
	second: {
		common: true,
		size: 1000,
		steps: 60
	},
	minute: {
		common: true,
		size: 60000,
		steps: 60
	},
	hour: {
		common: true,
		size: 3600000,
		steps: 24
	},
	day: {
		common: true,
		size: 86400000,
		steps: 30
	},
	week: {
		common: false,
		size: 604800000,
		steps: 4
	},
	month: {
		common: true,
		size: 2.628e9,
		steps: 12
	},
	quarter: {
		common: false,
		size: 7.884e9,
		steps: 4
	},
	year: {
		common: true,
		size: 3.154e10
	}
};

var UNITS = Object.keys(INTERVALS);

function sorter(a, b) {
	return a - b;
}

function arrayUnique(items) {
	var hash = {};
	var out = [];
	var i, ilen, item;

	for (i = 0, ilen = items.length; i < ilen; ++i) {
		item = items[i];
		if (!hash[item]) {
			hash[item] = true;
			out.push(item);
		}
	}

	return out;
}

function getMin(options) {
	return helpers$1.valueOrDefault(options.time.min, options.ticks.min);
}

function getMax(options) {
	return helpers$1.valueOrDefault(options.time.max, options.ticks.max);
}

/**
 * Returns an array of {time, pos} objects used to interpolate a specific `time` or position
 * (`pos`) on the scale, by searching entries before and after the requested value. `pos` is
 * a decimal between 0 and 1: 0 being the start of the scale (left or top) and 1 the other
 * extremity (left + width or top + height). Note that it would be more optimized to directly
 * store pre-computed pixels, but the scale dimensions are not guaranteed at the time we need
 * to create the lookup table. The table ALWAYS contains at least two items: min and max.
 *
 * @param {number[]} timestamps - timestamps sorted from lowest to highest.
 * @param {string} distribution - If 'linear', timestamps will be spread linearly along the min
 * and max range, so basically, the table will contains only two items: {min, 0} and {max, 1}.
 * If 'series', timestamps will be positioned at the same distance from each other. In this
 * case, only timestamps that break the time linearity are registered, meaning that in the
 * best case, all timestamps are linear, the table contains only min and max.
 */
function buildLookupTable(timestamps, min, max, distribution) {
	if (distribution === 'linear' || !timestamps.length) {
		return [
			{time: min, pos: 0},
			{time: max, pos: 1}
		];
	}

	var table = [];
	var items = [min];
	var i, ilen, prev, curr, next;

	for (i = 0, ilen = timestamps.length; i < ilen; ++i) {
		curr = timestamps[i];
		if (curr > min && curr < max) {
			items.push(curr);
		}
	}

	items.push(max);

	for (i = 0, ilen = items.length; i < ilen; ++i) {
		next = items[i + 1];
		prev = items[i - 1];
		curr = items[i];

		// only add points that breaks the scale linearity
		if (prev === undefined || next === undefined || Math.round((next + prev) / 2) !== curr) {
			table.push({time: curr, pos: i / (ilen - 1)});
		}
	}

	return table;
}

// @see adapted from https://www.anujgakhar.com/2014/03/01/binary-search-in-javascript/
function lookup(table, key, value) {
	var lo = 0;
	var hi = table.length - 1;
	var mid, i0, i1;

	while (lo >= 0 && lo <= hi) {
		mid = (lo + hi) >> 1;
		i0 = table[mid - 1] || null;
		i1 = table[mid];

		if (!i0) {
			// given value is outside table (before first item)
			return {lo: null, hi: i1};
		} else if (i1[key] < value) {
			lo = mid + 1;
		} else if (i0[key] > value) {
			hi = mid - 1;
		} else {
			return {lo: i0, hi: i1};
		}
	}

	// given value is outside table (after last item)
	return {lo: i1, hi: null};
}

/**
 * Linearly interpolates the given source `value` using the table items `skey` values and
 * returns the associated `tkey` value. For example, interpolate(table, 'time', 42, 'pos')
 * returns the position for a timestamp equal to 42. If value is out of bounds, values at
 * index [0, 1] or [n - 1, n] are used for the interpolation.
 */
function interpolate$1(table, skey, sval, tkey) {
	var range = lookup(table, skey, sval);

	// Note: the lookup table ALWAYS contains at least 2 items (min and max)
	var prev = !range.lo ? table[0] : !range.hi ? table[table.length - 2] : range.lo;
	var next = !range.lo ? table[1] : !range.hi ? table[table.length - 1] : range.hi;

	var span = next[skey] - prev[skey];
	var ratio = span ? (sval - prev[skey]) / span : 0;
	var offset = (next[tkey] - prev[tkey]) * ratio;

	return prev[tkey] + offset;
}

function toTimestamp(scale, input) {
	var adapter = scale._adapter;
	var options = scale.options.time;
	var parser = options.parser;
	var format = parser || options.format;
	var value = input;

	if (typeof parser === 'function') {
		value = parser(value);
	}

	// Only parse if its not a timestamp already
	if (!helpers$1.isFinite(value)) {
		value = typeof format === 'string'
			? adapter.parse(value, format)
			: adapter.parse(value);
	}

	if (value !== null) {
		return +value;
	}

	// Labels are in an incompatible format and no `parser` has been provided.
	// The user might still use the deprecated `format` option for parsing.
	if (!parser && typeof format === 'function') {
		value = format(input);

		// `format` could return something else than a timestamp, if so, parse it
		if (!helpers$1.isFinite(value)) {
			value = adapter.parse(value);
		}
	}

	return value;
}

function parse(scale, input) {
	if (helpers$1.isNullOrUndef(input)) {
		return null;
	}

	var options = scale.options.time;
	var value = toTimestamp(scale, scale.getRightValue(input));
	if (value === null) {
		return value;
	}

	if (options.round) {
		value = +scale._adapter.startOf(value, options.round);
	}

	return value;
}

/**
 * Figures out what unit results in an appropriate number of auto-generated ticks
 */
function determineUnitForAutoTicks(minUnit, min, max, capacity) {
	var ilen = UNITS.length;
	var i, interval, factor;

	for (i = UNITS.indexOf(minUnit); i < ilen - 1; ++i) {
		interval = INTERVALS[UNITS[i]];
		factor = interval.steps ? interval.steps : MAX_INTEGER;

		if (interval.common && Math.ceil((max - min) / (factor * interval.size)) <= capacity) {
			return UNITS[i];
		}
	}

	return UNITS[ilen - 1];
}

/**
 * Figures out what unit to format a set of ticks with
 */
function determineUnitForFormatting(scale, numTicks, minUnit, min, max) {
	var i, unit;

	for (i = UNITS.length - 1; i >= UNITS.indexOf(minUnit); i--) {
		unit = UNITS[i];
		if (INTERVALS[unit].common && scale._adapter.diff(max, min, unit) >= numTicks - 1) {
			return unit;
		}
	}

	return UNITS[minUnit ? UNITS.indexOf(minUnit) : 0];
}

function determineMajorUnit(unit) {
	for (var i = UNITS.indexOf(unit) + 1, ilen = UNITS.length; i < ilen; ++i) {
		if (INTERVALS[UNITS[i]].common) {
			return UNITS[i];
		}
	}
}

/**
 * Generates a maximum of `capacity` timestamps between min and max, rounded to the
 * `minor` unit using the given scale time `options`.
 * Important: this method can return ticks outside the min and max range, it's the
 * responsibility of the calling code to clamp values if needed.
 */
function generate(scale, min, max, capacity) {
	var adapter = scale._adapter;
	var options = scale.options;
	var timeOpts = options.time;
	var minor = timeOpts.unit || determineUnitForAutoTicks(timeOpts.minUnit, min, max, capacity);
	var stepSize = resolve$5([timeOpts.stepSize, timeOpts.unitStepSize, 1]);
	var weekday = minor === 'week' ? timeOpts.isoWeekday : false;
	var first = min;
	var ticks = [];
	var time;

	// For 'week' unit, handle the first day of week option
	if (weekday) {
		first = +adapter.startOf(first, 'isoWeek', weekday);
	}

	// Align first ticks on unit
	first = +adapter.startOf(first, weekday ? 'day' : minor);

	// Prevent browser from freezing in case user options request millions of milliseconds
	if (adapter.diff(max, min, minor) > 100000 * stepSize) {
		throw min + ' and ' + max + ' are too far apart with stepSize of ' + stepSize + ' ' + minor;
	}

	for (time = first; time < max; time = +adapter.add(time, stepSize, minor)) {
		ticks.push(time);
	}

	if (time === max || options.bounds === 'ticks') {
		ticks.push(time);
	}

	return ticks;
}

/**
 * Returns the start and end offsets from edges in the form of {start, end}
 * where each value is a relative width to the scale and ranges between 0 and 1.
 * They add extra margins on the both sides by scaling down the original scale.
 * Offsets are added when the `offset` option is true.
 */
function computeOffsets(table, ticks, min, max, options) {
	var start = 0;
	var end = 0;
	var first, last;

	if (options.offset && ticks.length) {
		first = interpolate$1(table, 'time', ticks[0], 'pos');
		if (ticks.length === 1) {
			start = 1 - first;
		} else {
			start = (interpolate$1(table, 'time', ticks[1], 'pos') - first) / 2;
		}
		last = interpolate$1(table, 'time', ticks[ticks.length - 1], 'pos');
		if (ticks.length === 1) {
			end = last;
		} else {
			end = (last - interpolate$1(table, 'time', ticks[ticks.length - 2], 'pos')) / 2;
		}
	}

	return {start: start, end: end, factor: 1 / (start + 1 + end)};
}

function setMajorTicks(scale, ticks, map, majorUnit) {
	var adapter = scale._adapter;
	var first = +adapter.startOf(ticks[0].value, majorUnit);
	var last = ticks[ticks.length - 1].value;
	var major, index;

	for (major = first; major <= last; major = +adapter.add(major, 1, majorUnit)) {
		index = map[major];
		if (index >= 0) {
			ticks[index].major = true;
		}
	}
	return ticks;
}

function ticksFromTimestamps(scale, values, majorUnit) {
	var ticks = [];
	var map = {};
	var ilen = values.length;
	var i, value;

	for (i = 0; i < ilen; ++i) {
		value = values[i];
		map[value] = i;

		ticks.push({
			value: value,
			major: false
		});
	}

	// We set the major ticks separately from the above loop because calling startOf for every tick
	// is expensive when there is a large number of ticks
	return (ilen === 0 || !majorUnit) ? ticks : setMajorTicks(scale, ticks, map, majorUnit);
}

var defaultConfig$4 = {
	position: 'bottom',

	/**
	 * Data distribution along the scale:
	 * - 'linear': data are spread according to their time (distances can vary),
	 * - 'series': data are spread at the same distance from each other.
	 * @see https://github.com/chartjs/Chart.js/pull/4507
	 * @since 2.7.0
	 */
	distribution: 'linear',

	/**
	 * Scale boundary strategy (bypassed by min/max time options)
	 * - `data`: make sure data are fully visible, ticks outside are removed
	 * - `ticks`: make sure ticks are fully visible, data outside are truncated
	 * @see https://github.com/chartjs/Chart.js/pull/4556
	 * @since 2.7.0
	 */
	bounds: 'data',

	adapters: {},
	time: {
		parser: false, // false == a pattern string from https://momentjs.com/docs/#/parsing/string-format/ or a custom callback that converts its argument to a moment
		unit: false, // false == automatic or override with week, month, year, etc.
		round: false, // none, or override with week, month, year, etc.
		displayFormat: false, // DEPRECATED
		isoWeekday: false, // override week start day - see https://momentjs.com/docs/#/get-set/iso-weekday/
		minUnit: 'millisecond',
		displayFormats: {}
	},
	ticks: {
		autoSkip: false,

		/**
		 * Ticks generation input values:
		 * - 'auto': generates "optimal" ticks based on scale size and time options.
		 * - 'data': generates ticks from data (including labels from data {t|x|y} objects).
		 * - 'labels': generates ticks from user given `data.labels` values ONLY.
		 * @see https://github.com/chartjs/Chart.js/pull/4507
		 * @since 2.7.0
		 */
		source: 'auto',

		major: {
			enabled: false
		}
	}
};

var scale_time = core_scale.extend({
	initialize: function() {
		this.mergeTicksOptions();
		core_scale.prototype.initialize.call(this);
	},

	update: function() {
		var me = this;
		var options = me.options;
		var time = options.time || (options.time = {});
		var adapter = me._adapter = new core_adapters._date(options.adapters.date);

		// DEPRECATIONS: output a message only one time per update
		deprecated$1('time scale', time.format, 'time.format', 'time.parser');
		deprecated$1('time scale', time.min, 'time.min', 'ticks.min');
		deprecated$1('time scale', time.max, 'time.max', 'ticks.max');

		// Backward compatibility: before introducing adapter, `displayFormats` was
		// supposed to contain *all* unit/string pairs but this can't be resolved
		// when loading the scale (adapters are loaded afterward), so let's populate
		// missing formats on update
		helpers$1.mergeIf(time.displayFormats, adapter.formats());

		return core_scale.prototype.update.apply(me, arguments);
	},

	/**
	 * Allows data to be referenced via 't' attribute
	 */
	getRightValue: function(rawValue) {
		if (rawValue && rawValue.t !== undefined) {
			rawValue = rawValue.t;
		}
		return core_scale.prototype.getRightValue.call(this, rawValue);
	},

	determineDataLimits: function() {
		var me = this;
		var chart = me.chart;
		var adapter = me._adapter;
		var options = me.options;
		var unit = options.time.unit || 'day';
		var min = MAX_INTEGER;
		var max = MIN_INTEGER;
		var timestamps = [];
		var datasets = [];
		var labels = [];
		var i, j, ilen, jlen, data, timestamp, labelsAdded;
		var dataLabels = me._getLabels();

		for (i = 0, ilen = dataLabels.length; i < ilen; ++i) {
			labels.push(parse(me, dataLabels[i]));
		}

		for (i = 0, ilen = (chart.data.datasets || []).length; i < ilen; ++i) {
			if (chart.isDatasetVisible(i)) {
				data = chart.data.datasets[i].data;

				// Let's consider that all data have the same format.
				if (helpers$1.isObject(data[0])) {
					datasets[i] = [];

					for (j = 0, jlen = data.length; j < jlen; ++j) {
						timestamp = parse(me, data[j]);
						timestamps.push(timestamp);
						datasets[i][j] = timestamp;
					}
				} else {
					datasets[i] = labels.slice(0);
					if (!labelsAdded) {
						timestamps = timestamps.concat(labels);
						labelsAdded = true;
					}
				}
			} else {
				datasets[i] = [];
			}
		}

		if (labels.length) {
			min = Math.min(min, labels[0]);
			max = Math.max(max, labels[labels.length - 1]);
		}

		if (timestamps.length) {
			timestamps = ilen > 1 ? arrayUnique(timestamps).sort(sorter) : timestamps.sort(sorter);
			min = Math.min(min, timestamps[0]);
			max = Math.max(max, timestamps[timestamps.length - 1]);
		}

		min = parse(me, getMin(options)) || min;
		max = parse(me, getMax(options)) || max;

		// In case there is no valid min/max, set limits based on unit time option
		min = min === MAX_INTEGER ? +adapter.startOf(Date.now(), unit) : min;
		max = max === MIN_INTEGER ? +adapter.endOf(Date.now(), unit) + 1 : max;

		// Make sure that max is strictly higher than min (required by the lookup table)
		me.min = Math.min(min, max);
		me.max = Math.max(min + 1, max);

		// PRIVATE
		me._table = [];
		me._timestamps = {
			data: timestamps,
			datasets: datasets,
			labels: labels
		};
	},

	buildTicks: function() {
		var me = this;
		var min = me.min;
		var max = me.max;
		var options = me.options;
		var tickOpts = options.ticks;
		var timeOpts = options.time;
		var timestamps = me._timestamps;
		var ticks = [];
		var capacity = me.getLabelCapacity(min);
		var source = tickOpts.source;
		var distribution = options.distribution;
		var i, ilen, timestamp;

		if (source === 'data' || (source === 'auto' && distribution === 'series')) {
			timestamps = timestamps.data;
		} else if (source === 'labels') {
			timestamps = timestamps.labels;
		} else {
			timestamps = generate(me, min, max, capacity);
		}

		if (options.bounds === 'ticks' && timestamps.length) {
			min = timestamps[0];
			max = timestamps[timestamps.length - 1];
		}

		// Enforce limits with user min/max options
		min = parse(me, getMin(options)) || min;
		max = parse(me, getMax(options)) || max;

		// Remove ticks outside the min/max range
		for (i = 0, ilen = timestamps.length; i < ilen; ++i) {
			timestamp = timestamps[i];
			if (timestamp >= min && timestamp <= max) {
				ticks.push(timestamp);
			}
		}

		me.min = min;
		me.max = max;

		// PRIVATE
		// determineUnitForFormatting relies on the number of ticks so we don't use it when
		// autoSkip is enabled because we don't yet know what the final number of ticks will be
		me._unit = timeOpts.unit || (tickOpts.autoSkip
			? determineUnitForAutoTicks(timeOpts.minUnit, me.min, me.max, capacity)
			: determineUnitForFormatting(me, ticks.length, timeOpts.minUnit, me.min, me.max));
		me._majorUnit = !tickOpts.major.enabled || me._unit === 'year' ? undefined
			: determineMajorUnit(me._unit);
		me._table = buildLookupTable(me._timestamps.data, min, max, distribution);
		me._offsets = computeOffsets(me._table, ticks, min, max, options);

		if (tickOpts.reverse) {
			ticks.reverse();
		}

		return ticksFromTimestamps(me, ticks, me._majorUnit);
	},

	getLabelForIndex: function(index, datasetIndex) {
		var me = this;
		var adapter = me._adapter;
		var data = me.chart.data;
		var timeOpts = me.options.time;
		var label = data.labels && index < data.labels.length ? data.labels[index] : '';
		var value = data.datasets[datasetIndex].data[index];

		if (helpers$1.isObject(value)) {
			label = me.getRightValue(value);
		}
		if (timeOpts.tooltipFormat) {
			return adapter.format(toTimestamp(me, label), timeOpts.tooltipFormat);
		}
		if (typeof label === 'string') {
			return label;
		}
		return adapter.format(toTimestamp(me, label), timeOpts.displayFormats.datetime);
	},

	/**
	 * Function to format an individual tick mark
	 * @private
	 */
	tickFormatFunction: function(time, index, ticks, format) {
		var me = this;
		var adapter = me._adapter;
		var options = me.options;
		var formats = options.time.displayFormats;
		var minorFormat = formats[me._unit];
		var majorUnit = me._majorUnit;
		var majorFormat = formats[majorUnit];
		var tick = ticks[index];
		var tickOpts = options.ticks;
		var major = majorUnit && majorFormat && tick && tick.major;
		var label = adapter.format(time, format ? format : major ? majorFormat : minorFormat);
		var nestedTickOpts = major ? tickOpts.major : tickOpts.minor;
		var formatter = resolve$5([
			nestedTickOpts.callback,
			nestedTickOpts.userCallback,
			tickOpts.callback,
			tickOpts.userCallback
		]);

		return formatter ? formatter(label, index, ticks) : label;
	},

	convertTicksToLabels: function(ticks) {
		var labels = [];
		var i, ilen;

		for (i = 0, ilen = ticks.length; i < ilen; ++i) {
			labels.push(this.tickFormatFunction(ticks[i].value, i, ticks));
		}

		return labels;
	},

	/**
	 * @private
	 */
	getPixelForOffset: function(time) {
		var me = this;
		var offsets = me._offsets;
		var pos = interpolate$1(me._table, 'time', time, 'pos');
		return me.getPixelForDecimal((offsets.start + pos) * offsets.factor);
	},

	getPixelForValue: function(value, index, datasetIndex) {
		var me = this;
		var time = null;

		if (index !== undefined && datasetIndex !== undefined) {
			time = me._timestamps.datasets[datasetIndex][index];
		}

		if (time === null) {
			time = parse(me, value);
		}

		if (time !== null) {
			return me.getPixelForOffset(time);
		}
	},

	getPixelForTick: function(index) {
		var ticks = this.getTicks();
		return index >= 0 && index < ticks.length ?
			this.getPixelForOffset(ticks[index].value) :
			null;
	},

	getValueForPixel: function(pixel) {
		var me = this;
		var offsets = me._offsets;
		var pos = me.getDecimalForPixel(pixel) / offsets.factor - offsets.end;
		var time = interpolate$1(me._table, 'pos', pos, 'time');

		// DEPRECATION, we should return time directly
		return me._adapter._create(time);
	},

	/**
	 * @private
	 */
	_getLabelSize: function(label) {
		var me = this;
		var ticksOpts = me.options.ticks;
		var tickLabelWidth = me.ctx.measureText(label).width;
		var angle = helpers$1.toRadians(me.isHorizontal() ? ticksOpts.maxRotation : ticksOpts.minRotation);
		var cosRotation = Math.cos(angle);
		var sinRotation = Math.sin(angle);
		var tickFontSize = valueOrDefault$d(ticksOpts.fontSize, core_defaults.global.defaultFontSize);

		return {
			w: (tickLabelWidth * cosRotation) + (tickFontSize * sinRotation),
			h: (tickLabelWidth * sinRotation) + (tickFontSize * cosRotation)
		};
	},

	/**
	 * Crude approximation of what the label width might be
	 * @private
	 */
	getLabelWidth: function(label) {
		return this._getLabelSize(label).w;
	},

	/**
	 * @private
	 */
	getLabelCapacity: function(exampleTime) {
		var me = this;
		var timeOpts = me.options.time;
		var displayFormats = timeOpts.displayFormats;

		// pick the longest format (milliseconds) for guestimation
		var format = displayFormats[timeOpts.unit] || displayFormats.millisecond;
		var exampleLabel = me.tickFormatFunction(exampleTime, 0, ticksFromTimestamps(me, [exampleTime], me._majorUnit), format);
		var size = me._getLabelSize(exampleLabel);
		var capacity = Math.floor(me.isHorizontal() ? me.width / size.w : me.height / size.h);

		if (me.options.offset) {
			capacity--;
		}

		return capacity > 0 ? capacity : 1;
	}
});

// INTERNAL: static default options, registered in src/index.js
var _defaults$4 = defaultConfig$4;
scale_time._defaults = _defaults$4;

var scales = {
	category: scale_category,
	linear: scale_linear,
	logarithmic: scale_logarithmic,
	radialLinear: scale_radialLinear,
	time: scale_time
};

var FORMATS = {
	datetime: 'MMM D, YYYY, h:mm:ss a',
	millisecond: 'h:mm:ss.SSS a',
	second: 'h:mm:ss a',
	minute: 'h:mm a',
	hour: 'hA',
	day: 'MMM D',
	week: 'll',
	month: 'MMM YYYY',
	quarter: '[Q]Q - YYYY',
	year: 'YYYY'
};

core_adapters._date.override(typeof moment === 'function' ? {
	_id: 'moment', // DEBUG ONLY

	formats: function() {
		return FORMATS;
	},

	parse: function(value, format) {
		if (typeof value === 'string' && typeof format === 'string') {
			value = moment(value, format);
		} else if (!(value instanceof moment)) {
			value = moment(value);
		}
		return value.isValid() ? value.valueOf() : null;
	},

	format: function(time, format) {
		return moment(time).format(format);
	},

	add: function(time, amount, unit) {
		return moment(time).add(amount, unit).valueOf();
	},

	diff: function(max, min, unit) {
		return moment(max).diff(moment(min), unit);
	},

	startOf: function(time, unit, weekday) {
		time = moment(time);
		if (unit === 'isoWeek') {
			return time.isoWeekday(weekday).valueOf();
		}
		return time.startOf(unit).valueOf();
	},

	endOf: function(time, unit) {
		return moment(time).endOf(unit).valueOf();
	},

	// DEPRECATIONS

	/**
	 * Provided for backward compatibility with scale.getValueForPixel().
	 * @deprecated since version 2.8.0
	 * @todo remove at version 3
	 * @private
	 */
	_create: function(time) {
		return moment(time);
	},
} : {});

core_defaults._set('global', {
	plugins: {
		filler: {
			propagate: true
		}
	}
});

var mappers = {
	dataset: function(source) {
		var index = source.fill;
		var chart = source.chart;
		var meta = chart.getDatasetMeta(index);
		var visible = meta && chart.isDatasetVisible(index);
		var points = (visible && meta.dataset._children) || [];
		var length = points.length || 0;

		return !length ? null : function(point, i) {
			return (i < length && points[i]._view) || null;
		};
	},

	boundary: function(source) {
		var boundary = source.boundary;
		var x = boundary ? boundary.x : null;
		var y = boundary ? boundary.y : null;

		if (helpers$1.isArray(boundary)) {
			return function(point, i) {
				return boundary[i];
			};
		}

		return function(point) {
			return {
				x: x === null ? point.x : x,
				y: y === null ? point.y : y,
			};
		};
	}
};

// @todo if (fill[0] === '#')
function decodeFill(el, index, count) {
	var model = el._model || {};
	var fill = model.fill;
	var target;

	if (fill === undefined) {
		fill = !!model.backgroundColor;
	}

	if (fill === false || fill === null) {
		return false;
	}

	if (fill === true) {
		return 'origin';
	}

	target = parseFloat(fill, 10);
	if (isFinite(target) && Math.floor(target) === target) {
		if (fill[0] === '-' || fill[0] === '+') {
			target = index + target;
		}

		if (target === index || target < 0 || target >= count) {
			return false;
		}

		return target;
	}

	switch (fill) {
	// compatibility
	case 'bottom':
		return 'start';
	case 'top':
		return 'end';
	case 'zero':
		return 'origin';
	// supported boundaries
	case 'origin':
	case 'start':
	case 'end':
		return fill;
	// invalid fill values
	default:
		return false;
	}
}

function computeLinearBoundary(source) {
	var model = source.el._model || {};
	var scale = source.el._scale || {};
	var fill = source.fill;
	var target = null;
	var horizontal;

	if (isFinite(fill)) {
		return null;
	}

	// Backward compatibility: until v3, we still need to support boundary values set on
	// the model (scaleTop, scaleBottom and scaleZero) because some external plugins and
	// controllers might still use it (e.g. the Smith chart).

	if (fill === 'start') {
		target = model.scaleBottom === undefined ? scale.bottom : model.scaleBottom;
	} else if (fill === 'end') {
		target = model.scaleTop === undefined ? scale.top : model.scaleTop;
	} else if (model.scaleZero !== undefined) {
		target = model.scaleZero;
	} else if (scale.getBasePixel) {
		target = scale.getBasePixel();
	}

	if (target !== undefined && target !== null) {
		if (target.x !== undefined && target.y !== undefined) {
			return target;
		}

		if (helpers$1.isFinite(target)) {
			horizontal = scale.isHorizontal();
			return {
				x: horizontal ? target : null,
				y: horizontal ? null : target
			};
		}
	}

	return null;
}

function computeCircularBoundary(source) {
	var scale = source.el._scale;
	var options = scale.options;
	var length = scale.chart.data.labels.length;
	var fill = source.fill;
	var target = [];
	var start, end, center, i, point;

	if (!length) {
		return null;
	}

	start = options.ticks.reverse ? scale.max : scale.min;
	end = options.ticks.reverse ? scale.min : scale.max;
	center = scale.getPointPositionForValue(0, start);
	for (i = 0; i < length; ++i) {
		point = fill === 'start' || fill === 'end'
			? scale.getPointPositionForValue(i, fill === 'start' ? start : end)
			: scale.getBasePosition(i);
		if (options.gridLines.circular) {
			point.cx = center.x;
			point.cy = center.y;
			point.angle = scale.getIndexAngle(i) - Math.PI / 2;
		}
		target.push(point);
	}
	return target;
}

function computeBoundary(source) {
	var scale = source.el._scale || {};

	if (scale.getPointPositionForValue) {
		return computeCircularBoundary(source);
	}
	return computeLinearBoundary(source);
}

function resolveTarget(sources, index, propagate) {
	var source = sources[index];
	var fill = source.fill;
	var visited = [index];
	var target;

	if (!propagate) {
		return fill;
	}

	while (fill !== false && visited.indexOf(fill) === -1) {
		if (!isFinite(fill)) {
			return fill;
		}

		target = sources[fill];
		if (!target) {
			return false;
		}

		if (target.visible) {
			return fill;
		}

		visited.push(fill);
		fill = target.fill;
	}

	return false;
}

function createMapper(source) {
	var fill = source.fill;
	var type = 'dataset';

	if (fill === false) {
		return null;
	}

	if (!isFinite(fill)) {
		type = 'boundary';
	}

	return mappers[type](source);
}

function isDrawable(point) {
	return point && !point.skip;
}

function drawArea(ctx, curve0, curve1, len0, len1) {
	var i, cx, cy, r;

	if (!len0 || !len1) {
		return;
	}

	// building first area curve (normal)
	ctx.moveTo(curve0[0].x, curve0[0].y);
	for (i = 1; i < len0; ++i) {
		helpers$1.canvas.lineTo(ctx, curve0[i - 1], curve0[i]);
	}

	if (curve1[0].angle !== undefined) {
		cx = curve1[0].cx;
		cy = curve1[0].cy;
		r = Math.sqrt(Math.pow(curve1[0].x - cx, 2) + Math.pow(curve1[0].y - cy, 2));
		for (i = len1 - 1; i > 0; --i) {
			ctx.arc(cx, cy, r, curve1[i].angle, curve1[i - 1].angle, true);
		}
		return;
	}

	// joining the two area curves
	ctx.lineTo(curve1[len1 - 1].x, curve1[len1 - 1].y);

	// building opposite area curve (reverse)
	for (i = len1 - 1; i > 0; --i) {
		helpers$1.canvas.lineTo(ctx, curve1[i], curve1[i - 1], true);
	}
}

function doFill(ctx, points, mapper, view, color, loop) {
	var count = points.length;
	var span = view.spanGaps;
	var curve0 = [];
	var curve1 = [];
	var len0 = 0;
	var len1 = 0;
	var i, ilen, index, p0, p1, d0, d1, loopOffset;

	ctx.beginPath();

	for (i = 0, ilen = count; i < ilen; ++i) {
		index = i % count;
		p0 = points[index]._view;
		p1 = mapper(p0, index, view);
		d0 = isDrawable(p0);
		d1 = isDrawable(p1);

		if (loop && loopOffset === undefined && d0) {
			loopOffset = i + 1;
			ilen = count + loopOffset;
		}

		if (d0 && d1) {
			len0 = curve0.push(p0);
			len1 = curve1.push(p1);
		} else if (len0 && len1) {
			if (!span) {
				drawArea(ctx, curve0, curve1, len0, len1);
				len0 = len1 = 0;
				curve0 = [];
				curve1 = [];
			} else {
				if (d0) {
					curve0.push(p0);
				}
				if (d1) {
					curve1.push(p1);
				}
			}
		}
	}

	drawArea(ctx, curve0, curve1, len0, len1);

	ctx.closePath();
	ctx.fillStyle = color;
	ctx.fill();
}

var plugin_filler = {
	id: 'filler',

	afterDatasetsUpdate: function(chart, options) {
		var count = (chart.data.datasets || []).length;
		var propagate = options.propagate;
		var sources = [];
		var meta, i, el, source;

		for (i = 0; i < count; ++i) {
			meta = chart.getDatasetMeta(i);
			el = meta.dataset;
			source = null;

			if (el && el._model && el instanceof elements.Line) {
				source = {
					visible: chart.isDatasetVisible(i),
					fill: decodeFill(el, i, count),
					chart: chart,
					el: el
				};
			}

			meta.$filler = source;
			sources.push(source);
		}

		for (i = 0; i < count; ++i) {
			source = sources[i];
			if (!source) {
				continue;
			}

			source.fill = resolveTarget(sources, i, propagate);
			source.boundary = computeBoundary(source);
			source.mapper = createMapper(source);
		}
	},

	beforeDatasetsDraw: function(chart) {
		var metasets = chart._getSortedVisibleDatasetMetas();
		var ctx = chart.ctx;
		var meta, i, el, view, points, mapper, color;

		for (i = metasets.length - 1; i >= 0; --i) {
			meta = metasets[i].$filler;

			if (!meta || !meta.visible) {
				continue;
			}

			el = meta.el;
			view = el._view;
			points = el._children || [];
			mapper = meta.mapper;
			color = view.backgroundColor || core_defaults.global.defaultColor;

			if (mapper && color && points.length) {
				helpers$1.canvas.clipArea(ctx, chart.chartArea);
				doFill(ctx, points, mapper, view, color, el._loop);
				helpers$1.canvas.unclipArea(ctx);
			}
		}
	}
};

var getRtlHelper$1 = helpers$1.rtl.getRtlAdapter;
var noop$1 = helpers$1.noop;
var valueOrDefault$e = helpers$1.valueOrDefault;

core_defaults._set('global', {
	legend: {
		display: true,
		position: 'top',
		align: 'center',
		fullWidth: true,
		reverse: false,
		weight: 1000,

		// a callback that will handle
		onClick: function(e, legendItem) {
			var index = legendItem.datasetIndex;
			var ci = this.chart;
			var meta = ci.getDatasetMeta(index);

			// See controller.isDatasetVisible comment
			meta.hidden = meta.hidden === null ? !ci.data.datasets[index].hidden : null;

			// We hid a dataset ... rerender the chart
			ci.update();
		},

		onHover: null,
		onLeave: null,

		labels: {
			boxWidth: 40,
			padding: 10,
			// Generates labels shown in the legend
			// Valid properties to return:
			// text : text to display
			// fillStyle : fill of coloured box
			// strokeStyle: stroke of coloured box
			// hidden : if this legend item refers to a hidden item
			// lineCap : cap style for line
			// lineDash
			// lineDashOffset :
			// lineJoin :
			// lineWidth :
			generateLabels: function(chart) {
				var datasets = chart.data.datasets;
				var options = chart.options.legend || {};
				var usePointStyle = options.labels && options.labels.usePointStyle;

				return chart._getSortedDatasetMetas().map(function(meta) {
					var style = meta.controller.getStyle(usePointStyle ? 0 : undefined);

					return {
						text: datasets[meta.index].label,
						fillStyle: style.backgroundColor,
						hidden: !chart.isDatasetVisible(meta.index),
						lineCap: style.borderCapStyle,
						lineDash: style.borderDash,
						lineDashOffset: style.borderDashOffset,
						lineJoin: style.borderJoinStyle,
						lineWidth: style.borderWidth,
						strokeStyle: style.borderColor,
						pointStyle: style.pointStyle,
						rotation: style.rotation,

						// Below is extra data used for toggling the datasets
						datasetIndex: meta.index
					};
				}, this);
			}
		}
	},

	legendCallback: function(chart) {
		var list = document.createElement('ul');
		var datasets = chart.data.datasets;
		var i, ilen, listItem, listItemSpan;

		list.setAttribute('class', chart.id + '-legend');

		for (i = 0, ilen = datasets.length; i < ilen; i++) {
			listItem = list.appendChild(document.createElement('li'));
			listItemSpan = listItem.appendChild(document.createElement('span'));
			listItemSpan.style.backgroundColor = datasets[i].backgroundColor;
			if (datasets[i].label) {
				listItem.appendChild(document.createTextNode(datasets[i].label));
			}
		}

		return list.outerHTML;
	}
});

/**
 * Helper function to get the box width based on the usePointStyle option
 * @param {object} labelopts - the label options on the legend
 * @param {number} fontSize - the label font size
 * @return {number} width of the color box area
 */
function getBoxWidth(labelOpts, fontSize) {
	return labelOpts.usePointStyle && labelOpts.boxWidth > fontSize ?
		fontSize :
		labelOpts.boxWidth;
}

/**
 * IMPORTANT: this class is exposed publicly as Chart.Legend, backward compatibility required!
 */
var Legend = core_element.extend({

	initialize: function(config) {
		var me = this;
		helpers$1.extend(me, config);

		// Contains hit boxes for each dataset (in dataset order)
		me.legendHitBoxes = [];

		/**
 		 * @private
 		 */
		me._hoveredItem = null;

		// Are we in doughnut mode which has a different data type
		me.doughnutMode = false;
	},

	// These methods are ordered by lifecycle. Utilities then follow.
	// Any function defined here is inherited by all legend types.
	// Any function can be extended by the legend type

	beforeUpdate: noop$1,
	update: function(maxWidth, maxHeight, margins) {
		var me = this;

		// Update Lifecycle - Probably don't want to ever extend or overwrite this function ;)
		me.beforeUpdate();

		// Absorb the master measurements
		me.maxWidth = maxWidth;
		me.maxHeight = maxHeight;
		me.margins = margins;

		// Dimensions
		me.beforeSetDimensions();
		me.setDimensions();
		me.afterSetDimensions();
		// Labels
		me.beforeBuildLabels();
		me.buildLabels();
		me.afterBuildLabels();

		// Fit
		me.beforeFit();
		me.fit();
		me.afterFit();
		//
		me.afterUpdate();

		return me.minSize;
	},
	afterUpdate: noop$1,

	//

	beforeSetDimensions: noop$1,
	setDimensions: function() {
		var me = this;
		// Set the unconstrained dimension before label rotation
		if (me.isHorizontal()) {
			// Reset position before calculating rotation
			me.width = me.maxWidth;
			me.left = 0;
			me.right = me.width;
		} else {
			me.height = me.maxHeight;

			// Reset position before calculating rotation
			me.top = 0;
			me.bottom = me.height;
		}

		// Reset padding
		me.paddingLeft = 0;
		me.paddingTop = 0;
		me.paddingRight = 0;
		me.paddingBottom = 0;

		// Reset minSize
		me.minSize = {
			width: 0,
			height: 0
		};
	},
	afterSetDimensions: noop$1,

	//

	beforeBuildLabels: noop$1,
	buildLabels: function() {
		var me = this;
		var labelOpts = me.options.labels || {};
		var legendItems = helpers$1.callback(labelOpts.generateLabels, [me.chart], me) || [];

		if (labelOpts.filter) {
			legendItems = legendItems.filter(function(item) {
				return labelOpts.filter(item, me.chart.data);
			});
		}

		if (me.options.reverse) {
			legendItems.reverse();
		}

		me.legendItems = legendItems;
	},
	afterBuildLabels: noop$1,

	//

	beforeFit: noop$1,
	fit: function() {
		var me = this;
		var opts = me.options;
		var labelOpts = opts.labels;
		var display = opts.display;

		var ctx = me.ctx;

		var labelFont = helpers$1.options._parseFont(labelOpts);
		var fontSize = labelFont.size;

		// Reset hit boxes
		var hitboxes = me.legendHitBoxes = [];

		var minSize = me.minSize;
		var isHorizontal = me.isHorizontal();

		if (isHorizontal) {
			minSize.width = me.maxWidth; // fill all the width
			minSize.height = display ? 10 : 0;
		} else {
			minSize.width = display ? 10 : 0;
			minSize.height = me.maxHeight; // fill all the height
		}

		// Increase sizes here
		if (!display) {
			me.width = minSize.width = me.height = minSize.height = 0;
			return;
		}
		ctx.font = labelFont.string;

		if (isHorizontal) {
			// Labels

			// Width of each line of legend boxes. Labels wrap onto multiple lines when there are too many to fit on one
			var lineWidths = me.lineWidths = [0];
			var totalHeight = 0;

			ctx.textAlign = 'left';
			ctx.textBaseline = 'middle';

			helpers$1.each(me.legendItems, function(legendItem, i) {
				var boxWidth = getBoxWidth(labelOpts, fontSize);
				var width = boxWidth + (fontSize / 2) + ctx.measureText(legendItem.text).width;

				if (i === 0 || lineWidths[lineWidths.length - 1] + width + 2 * labelOpts.padding > minSize.width) {
					totalHeight += fontSize + labelOpts.padding;
					lineWidths[lineWidths.length - (i > 0 ? 0 : 1)] = 0;
				}

				// Store the hitbox width and height here. Final position will be updated in `draw`
				hitboxes[i] = {
					left: 0,
					top: 0,
					width: width,
					height: fontSize
				};

				lineWidths[lineWidths.length - 1] += width + labelOpts.padding;
			});

			minSize.height += totalHeight;

		} else {
			var vPadding = labelOpts.padding;
			var columnWidths = me.columnWidths = [];
			var columnHeights = me.columnHeights = [];
			var totalWidth = labelOpts.padding;
			var currentColWidth = 0;
			var currentColHeight = 0;

			helpers$1.each(me.legendItems, function(legendItem, i) {
				var boxWidth = getBoxWidth(labelOpts, fontSize);
				var itemWidth = boxWidth + (fontSize / 2) + ctx.measureText(legendItem.text).width;

				// If too tall, go to new column
				if (i > 0 && currentColHeight + fontSize + 2 * vPadding > minSize.height) {
					totalWidth += currentColWidth + labelOpts.padding;
					columnWidths.push(currentColWidth); // previous column width
					columnHeights.push(currentColHeight);
					currentColWidth = 0;
					currentColHeight = 0;
				}

				// Get max width
				currentColWidth = Math.max(currentColWidth, itemWidth);
				currentColHeight += fontSize + vPadding;

				// Store the hitbox width and height here. Final position will be updated in `draw`
				hitboxes[i] = {
					left: 0,
					top: 0,
					width: itemWidth,
					height: fontSize
				};
			});

			totalWidth += currentColWidth;
			columnWidths.push(currentColWidth);
			columnHeights.push(currentColHeight);
			minSize.width += totalWidth;
		}

		me.width = minSize.width;
		me.height = minSize.height;
	},
	afterFit: noop$1,

	// Shared Methods
	isHorizontal: function() {
		return this.options.position === 'top' || this.options.position === 'bottom';
	},

	// Actually draw the legend on the canvas
	draw: function() {
		var me = this;
		var opts = me.options;
		var labelOpts = opts.labels;
		var globalDefaults = core_defaults.global;
		var defaultColor = globalDefaults.defaultColor;
		var lineDefault = globalDefaults.elements.line;
		var legendHeight = me.height;
		var columnHeights = me.columnHeights;
		var legendWidth = me.width;
		var lineWidths = me.lineWidths;

		if (!opts.display) {
			return;
		}

		var rtlHelper = getRtlHelper$1(opts.rtl, me.left, me.minSize.width);
		var ctx = me.ctx;
		var fontColor = valueOrDefault$e(labelOpts.fontColor, globalDefaults.defaultFontColor);
		var labelFont = helpers$1.options._parseFont(labelOpts);
		var fontSize = labelFont.size;
		var cursor;

		// Canvas setup
		ctx.textAlign = rtlHelper.textAlign('left');
		ctx.textBaseline = 'middle';
		ctx.lineWidth = 0.5;
		ctx.strokeStyle = fontColor; // for strikethrough effect
		ctx.fillStyle = fontColor; // render in correct colour
		ctx.font = labelFont.string;

		var boxWidth = getBoxWidth(labelOpts, fontSize);
		var hitboxes = me.legendHitBoxes;

		// current position
		var drawLegendBox = function(x, y, legendItem) {
			if (isNaN(boxWidth) || boxWidth <= 0) {
				return;
			}

			// Set the ctx for the box
			ctx.save();

			var lineWidth = valueOrDefault$e(legendItem.lineWidth, lineDefault.borderWidth);
			ctx.fillStyle = valueOrDefault$e(legendItem.fillStyle, defaultColor);
			ctx.lineCap = valueOrDefault$e(legendItem.lineCap, lineDefault.borderCapStyle);
			ctx.lineDashOffset = valueOrDefault$e(legendItem.lineDashOffset, lineDefault.borderDashOffset);
			ctx.lineJoin = valueOrDefault$e(legendItem.lineJoin, lineDefault.borderJoinStyle);
			ctx.lineWidth = lineWidth;
			ctx.strokeStyle = valueOrDefault$e(legendItem.strokeStyle, defaultColor);

			if (ctx.setLineDash) {
				// IE 9 and 10 do not support line dash
				ctx.setLineDash(valueOrDefault$e(legendItem.lineDash, lineDefault.borderDash));
			}

			if (labelOpts && labelOpts.usePointStyle) {
				// Recalculate x and y for drawPoint() because its expecting
				// x and y to be center of figure (instead of top left)
				var radius = boxWidth * Math.SQRT2 / 2;
				var centerX = rtlHelper.xPlus(x, boxWidth / 2);
				var centerY = y + fontSize / 2;

				// Draw pointStyle as legend symbol
				helpers$1.canvas.drawPoint(ctx, legendItem.pointStyle, radius, centerX, centerY, legendItem.rotation);
			} else {
				// Draw box as legend symbol
				ctx.fillRect(rtlHelper.leftForLtr(x, boxWidth), y, boxWidth, fontSize);
				if (lineWidth !== 0) {
					ctx.strokeRect(rtlHelper.leftForLtr(x, boxWidth), y, boxWidth, fontSize);
				}
			}

			ctx.restore();
		};

		var fillText = function(x, y, legendItem, textWidth) {
			var halfFontSize = fontSize / 2;
			var xLeft = rtlHelper.xPlus(x, boxWidth + halfFontSize);
			var yMiddle = y + halfFontSize;

			ctx.fillText(legendItem.text, xLeft, yMiddle);

			if (legendItem.hidden) {
				// Strikethrough the text if hidden
				ctx.beginPath();
				ctx.lineWidth = 2;
				ctx.moveTo(xLeft, yMiddle);
				ctx.lineTo(rtlHelper.xPlus(xLeft, textWidth), yMiddle);
				ctx.stroke();
			}
		};

		var alignmentOffset = function(dimension, blockSize) {
			switch (opts.align) {
			case 'start':
				return labelOpts.padding;
			case 'end':
				return dimension - blockSize;
			default: // center
				return (dimension - blockSize + labelOpts.padding) / 2;
			}
		};

		// Horizontal
		var isHorizontal = me.isHorizontal();
		if (isHorizontal) {
			cursor = {
				x: me.left + alignmentOffset(legendWidth, lineWidths[0]),
				y: me.top + labelOpts.padding,
				line: 0
			};
		} else {
			cursor = {
				x: me.left + labelOpts.padding,
				y: me.top + alignmentOffset(legendHeight, columnHeights[0]),
				line: 0
			};
		}

		helpers$1.rtl.overrideTextDirection(me.ctx, opts.textDirection);

		var itemHeight = fontSize + labelOpts.padding;
		helpers$1.each(me.legendItems, function(legendItem, i) {
			var textWidth = ctx.measureText(legendItem.text).width;
			var width = boxWidth + (fontSize / 2) + textWidth;
			var x = cursor.x;
			var y = cursor.y;

			rtlHelper.setWidth(me.minSize.width);

			// Use (me.left + me.minSize.width) and (me.top + me.minSize.height)
			// instead of me.right and me.bottom because me.width and me.height
			// may have been changed since me.minSize was calculated
			if (isHorizontal) {
				if (i > 0 && x + width + labelOpts.padding > me.left + me.minSize.width) {
					y = cursor.y += itemHeight;
					cursor.line++;
					x = cursor.x = me.left + alignmentOffset(legendWidth, lineWidths[cursor.line]);
				}
			} else if (i > 0 && y + itemHeight > me.top + me.minSize.height) {
				x = cursor.x = x + me.columnWidths[cursor.line] + labelOpts.padding;
				cursor.line++;
				y = cursor.y = me.top + alignmentOffset(legendHeight, columnHeights[cursor.line]);
			}

			var realX = rtlHelper.x(x);

			drawLegendBox(realX, y, legendItem);

			hitboxes[i].left = rtlHelper.leftForLtr(realX, hitboxes[i].width);
			hitboxes[i].top = y;

			// Fill the actual label
			fillText(realX, y, legendItem, textWidth);

			if (isHorizontal) {
				cursor.x += width + labelOpts.padding;
			} else {
				cursor.y += itemHeight;
			}
		});

		helpers$1.rtl.restoreTextDirection(me.ctx, opts.textDirection);
	},

	/**
	 * @private
	 */
	_getLegendItemAt: function(x, y) {
		var me = this;
		var i, hitBox, lh;

		if (x >= me.left && x <= me.right && y >= me.top && y <= me.bottom) {
			// See if we are touching one of the dataset boxes
			lh = me.legendHitBoxes;
			for (i = 0; i < lh.length; ++i) {
				hitBox = lh[i];

				if (x >= hitBox.left && x <= hitBox.left + hitBox.width && y >= hitBox.top && y <= hitBox.top + hitBox.height) {
					// Touching an element
					return me.legendItems[i];
				}
			}
		}

		return null;
	},

	/**
	 * Handle an event
	 * @private
	 * @param {IEvent} event - The event to handle
	 */
	handleEvent: function(e) {
		var me = this;
		var opts = me.options;
		var type = e.type === 'mouseup' ? 'click' : e.type;
		var hoveredItem;

		if (type === 'mousemove') {
			if (!opts.onHover && !opts.onLeave) {
				return;
			}
		} else if (type === 'click') {
			if (!opts.onClick) {
				return;
			}
		} else {
			return;
		}

		// Chart event already has relative position in it
		hoveredItem = me._getLegendItemAt(e.x, e.y);

		if (type === 'click') {
			if (hoveredItem && opts.onClick) {
				// use e.native for backwards compatibility
				opts.onClick.call(me, e.native, hoveredItem);
			}
		} else {
			if (opts.onLeave && hoveredItem !== me._hoveredItem) {
				if (me._hoveredItem) {
					opts.onLeave.call(me, e.native, me._hoveredItem);
				}
				me._hoveredItem = hoveredItem;
			}

			if (opts.onHover && hoveredItem) {
				// use e.native for backwards compatibility
				opts.onHover.call(me, e.native, hoveredItem);
			}
		}
	}
});

function createNewLegendAndAttach(chart, legendOpts) {
	var legend = new Legend({
		ctx: chart.ctx,
		options: legendOpts,
		chart: chart
	});

	core_layouts.configure(chart, legend, legendOpts);
	core_layouts.addBox(chart, legend);
	chart.legend = legend;
}

var plugin_legend = {
	id: 'legend',

	/**
	 * Backward compatibility: since 2.1.5, the legend is registered as a plugin, making
	 * Chart.Legend obsolete. To avoid a breaking change, we export the Legend as part of
	 * the plugin, which one will be re-exposed in the chart.js file.
	 * https://github.com/chartjs/Chart.js/pull/2640
	 * @private
	 */
	_element: Legend,

	beforeInit: function(chart) {
		var legendOpts = chart.options.legend;

		if (legendOpts) {
			createNewLegendAndAttach(chart, legendOpts);
		}
	},

	beforeUpdate: function(chart) {
		var legendOpts = chart.options.legend;
		var legend = chart.legend;

		if (legendOpts) {
			helpers$1.mergeIf(legendOpts, core_defaults.global.legend);

			if (legend) {
				core_layouts.configure(chart, legend, legendOpts);
				legend.options = legendOpts;
			} else {
				createNewLegendAndAttach(chart, legendOpts);
			}
		} else if (legend) {
			core_layouts.removeBox(chart, legend);
			delete chart.legend;
		}
	},

	afterEvent: function(chart, e) {
		var legend = chart.legend;
		if (legend) {
			legend.handleEvent(e);
		}
	}
};

var noop$2 = helpers$1.noop;

core_defaults._set('global', {
	title: {
		display: false,
		fontStyle: 'bold',
		fullWidth: true,
		padding: 10,
		position: 'top',
		text: '',
		weight: 2000         // by default greater than legend (1000) to be above
	}
});

/**
 * IMPORTANT: this class is exposed publicly as Chart.Legend, backward compatibility required!
 */
var Title = core_element.extend({
	initialize: function(config) {
		var me = this;
		helpers$1.extend(me, config);

		// Contains hit boxes for each dataset (in dataset order)
		me.legendHitBoxes = [];
	},

	// These methods are ordered by lifecycle. Utilities then follow.

	beforeUpdate: noop$2,
	update: function(maxWidth, maxHeight, margins) {
		var me = this;

		// Update Lifecycle - Probably don't want to ever extend or overwrite this function ;)
		me.beforeUpdate();

		// Absorb the master measurements
		me.maxWidth = maxWidth;
		me.maxHeight = maxHeight;
		me.margins = margins;

		// Dimensions
		me.beforeSetDimensions();
		me.setDimensions();
		me.afterSetDimensions();
		// Labels
		me.beforeBuildLabels();
		me.buildLabels();
		me.afterBuildLabels();

		// Fit
		me.beforeFit();
		me.fit();
		me.afterFit();
		//
		me.afterUpdate();

		return me.minSize;

	},
	afterUpdate: noop$2,

	//

	beforeSetDimensions: noop$2,
	setDimensions: function() {
		var me = this;
		// Set the unconstrained dimension before label rotation
		if (me.isHorizontal()) {
			// Reset position before calculating rotation
			me.width = me.maxWidth;
			me.left = 0;
			me.right = me.width;
		} else {
			me.height = me.maxHeight;

			// Reset position before calculating rotation
			me.top = 0;
			me.bottom = me.height;
		}

		// Reset padding
		me.paddingLeft = 0;
		me.paddingTop = 0;
		me.paddingRight = 0;
		me.paddingBottom = 0;

		// Reset minSize
		me.minSize = {
			width: 0,
			height: 0
		};
	},
	afterSetDimensions: noop$2,

	//

	beforeBuildLabels: noop$2,
	buildLabels: noop$2,
	afterBuildLabels: noop$2,

	//

	beforeFit: noop$2,
	fit: function() {
		var me = this;
		var opts = me.options;
		var minSize = me.minSize = {};
		var isHorizontal = me.isHorizontal();
		var lineCount, textSize;

		if (!opts.display) {
			me.width = minSize.width = me.height = minSize.height = 0;
			return;
		}

		lineCount = helpers$1.isArray(opts.text) ? opts.text.length : 1;
		textSize = lineCount * helpers$1.options._parseFont(opts).lineHeight + opts.padding * 2;

		me.width = minSize.width = isHorizontal ? me.maxWidth : textSize;
		me.height = minSize.height = isHorizontal ? textSize : me.maxHeight;
	},
	afterFit: noop$2,

	// Shared Methods
	isHorizontal: function() {
		var pos = this.options.position;
		return pos === 'top' || pos === 'bottom';
	},

	// Actually draw the title block on the canvas
	draw: function() {
		var me = this;
		var ctx = me.ctx;
		var opts = me.options;

		if (!opts.display) {
			return;
		}

		var fontOpts = helpers$1.options._parseFont(opts);
		var lineHeight = fontOpts.lineHeight;
		var offset = lineHeight / 2 + opts.padding;
		var rotation = 0;
		var top = me.top;
		var left = me.left;
		var bottom = me.bottom;
		var right = me.right;
		var maxWidth, titleX, titleY;

		ctx.fillStyle = helpers$1.valueOrDefault(opts.fontColor, core_defaults.global.defaultFontColor); // render in correct colour
		ctx.font = fontOpts.string;

		// Horizontal
		if (me.isHorizontal()) {
			titleX = left + ((right - left) / 2); // midpoint of the width
			titleY = top + offset;
			maxWidth = right - left;
		} else {
			titleX = opts.position === 'left' ? left + offset : right - offset;
			titleY = top + ((bottom - top) / 2);
			maxWidth = bottom - top;
			rotation = Math.PI * (opts.position === 'left' ? -0.5 : 0.5);
		}

		ctx.save();
		ctx.translate(titleX, titleY);
		ctx.rotate(rotation);
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';

		var text = opts.text;
		if (helpers$1.isArray(text)) {
			var y = 0;
			for (var i = 0; i < text.length; ++i) {
				ctx.fillText(text[i], 0, y, maxWidth);
				y += lineHeight;
			}
		} else {
			ctx.fillText(text, 0, 0, maxWidth);
		}

		ctx.restore();
	}
});

function createNewTitleBlockAndAttach(chart, titleOpts) {
	var title = new Title({
		ctx: chart.ctx,
		options: titleOpts,
		chart: chart
	});

	core_layouts.configure(chart, title, titleOpts);
	core_layouts.addBox(chart, title);
	chart.titleBlock = title;
}

var plugin_title = {
	id: 'title',

	/**
	 * Backward compatibility: since 2.1.5, the title is registered as a plugin, making
	 * Chart.Title obsolete. To avoid a breaking change, we export the Title as part of
	 * the plugin, which one will be re-exposed in the chart.js file.
	 * https://github.com/chartjs/Chart.js/pull/2640
	 * @private
	 */
	_element: Title,

	beforeInit: function(chart) {
		var titleOpts = chart.options.title;

		if (titleOpts) {
			createNewTitleBlockAndAttach(chart, titleOpts);
		}
	},

	beforeUpdate: function(chart) {
		var titleOpts = chart.options.title;
		var titleBlock = chart.titleBlock;

		if (titleOpts) {
			helpers$1.mergeIf(titleOpts, core_defaults.global.title);

			if (titleBlock) {
				core_layouts.configure(chart, titleBlock, titleOpts);
				titleBlock.options = titleOpts;
			} else {
				createNewTitleBlockAndAttach(chart, titleOpts);
			}
		} else if (titleBlock) {
			core_layouts.removeBox(chart, titleBlock);
			delete chart.titleBlock;
		}
	}
};

var plugins = {};
var filler = plugin_filler;
var legend = plugin_legend;
var title = plugin_title;
plugins.filler = filler;
plugins.legend = legend;
plugins.title = title;

/**
 * @namespace Chart
 */


core_controller.helpers = helpers$1;

// @todo dispatch these helpers into appropriated helpers/helpers.* file and write unit tests!
core_helpers();

core_controller._adapters = core_adapters;
core_controller.Animation = core_animation;
core_controller.animationService = core_animations;
core_controller.controllers = controllers;
core_controller.DatasetController = core_datasetController;
core_controller.defaults = core_defaults;
core_controller.Element = core_element;
core_controller.elements = elements;
core_controller.Interaction = core_interaction;
core_controller.layouts = core_layouts;
core_controller.platform = platform;
core_controller.plugins = core_plugins;
core_controller.Scale = core_scale;
core_controller.scaleService = core_scaleService;
core_controller.Ticks = core_ticks;
core_controller.Tooltip = core_tooltip;

// Register built-in scales

core_controller.helpers.each(scales, function(scale, type) {
	core_controller.scaleService.registerScaleType(type, scale, scale._defaults);
});

// Load to register built-in adapters (as side effects)


// Loading built-in plugins

for (var k in plugins) {
	if (plugins.hasOwnProperty(k)) {
		core_controller.plugins.register(plugins[k]);
	}
}

core_controller.platform.initialize();

var src = core_controller;
if (typeof window !== 'undefined') {
	window.Chart = core_controller;
}

// DEPRECATIONS

/**
 * Provided for backward compatibility, not available anymore
 * @namespace Chart.Chart
 * @deprecated since version 2.8.0
 * @todo remove at version 3
 * @private
 */
core_controller.Chart = core_controller;

/**
 * Provided for backward compatibility, not available anymore
 * @namespace Chart.Legend
 * @deprecated since version 2.1.5
 * @todo remove at version 3
 * @private
 */
core_controller.Legend = plugins.legend._element;

/**
 * Provided for backward compatibility, not available anymore
 * @namespace Chart.Title
 * @deprecated since version 2.1.5
 * @todo remove at version 3
 * @private
 */
core_controller.Title = plugins.title._element;

/**
 * Provided for backward compatibility, use Chart.plugins instead
 * @namespace Chart.pluginService
 * @deprecated since version 2.1.5
 * @todo remove at version 3
 * @private
 */
core_controller.pluginService = core_controller.plugins;

/**
 * Provided for backward compatibility, inheriting from Chart.PlugingBase has no
 * effect, instead simply create/register plugins via plain JavaScript objects.
 * @interface Chart.PluginBase
 * @deprecated since version 2.5.0
 * @todo remove at version 3
 * @private
 */
core_controller.PluginBase = core_controller.Element.extend({});

/**
 * Provided for backward compatibility, use Chart.helpers.canvas instead.
 * @namespace Chart.canvasHelpers
 * @deprecated since version 2.6.0
 * @todo remove at version 3
 * @private
 */
core_controller.canvasHelpers = core_controller.helpers.canvas;

/**
 * Provided for backward compatibility, use Chart.layouts instead.
 * @namespace Chart.layoutService
 * @deprecated since version 2.7.3
 * @todo remove at version 3
 * @private
 */
core_controller.layoutService = core_controller.layouts;

/**
 * Provided for backward compatibility, not available anymore.
 * @namespace Chart.LinearScaleBase
 * @deprecated since version 2.8
 * @todo remove at version 3
 * @private
 */
core_controller.LinearScaleBase = scale_linearbase;

/**
 * Provided for backward compatibility, instead we should create a new Chart
 * by setting the type in the config (`new Chart(id, {type: '{chart-type}'}`).
 * @deprecated since version 2.8.0
 * @todo remove at version 3
 */
core_controller.helpers.each(
	[
		'Bar',
		'Bubble',
		'Doughnut',
		'Line',
		'PolarArea',
		'Radar',
		'Scatter'
	],
	function(klass) {
		core_controller[klass] = function(ctx, cfg) {
			return new core_controller(ctx, core_controller.helpers.merge(cfg || {}, {
				type: klass.charAt(0).toLowerCase() + klass.slice(1)
			}));
		};
	}
);

return src;

})));

/*!
 * angular-chart.js - An angular.js wrapper for Chart.js
 * http://jtblin.github.io/angular-chart.js/
 * Version: 1.1.1
 *
 * Copyright 2016 Jerome Touffe-Blin
 * Released under the BSD-2-Clause license
 * https://github.com/jtblin/angular-chart.js/blob/master/LICENSE
 */
(function (factory) {
  'use strict';
  if (typeof exports === 'object') {
    // Node/CommonJS
    module.exports = factory(
      typeof angular !== 'undefined' ? angular : require('angular'),
      typeof Chart !== 'undefined' ? Chart : require('chart.js'));
  }  else if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module.
    define(['angular', 'chart'], factory);
  } else {
    // Browser globals
    if (typeof angular === 'undefined') {
        throw new Error('AngularJS framework needs to be included, see https://angularjs.org/');
    } else if (typeof Chart === 'undefined') {
      throw new Error('Chart.js library needs to be included, see http://jtblin.github.io/angular-chart.js/');
    }
    factory(angular, Chart);
  }
}(function (angular, Chart) {
  'use strict';

  Chart.defaults.global.multiTooltipTemplate = '<%if (datasetLabel){%><%=datasetLabel%>: <%}%><%= value %>';
  Chart.defaults.global.tooltips.mode = 'label';
  Chart.defaults.global.elements.line.borderWidth = 2;
  Chart.defaults.global.elements.rectangle.borderWidth = 2;
  Chart.defaults.global.legend.display = false;
  Chart.defaults.global.colors = [
    '#97BBCD', // blue
    '#DCDCDC', // light grey
    '#F7464A', // red
    '#46BFBD', // green
    '#FDB45C', // yellow
    '#949FB1', // grey
    '#4D5360'  // dark grey
  ];

  var useExcanvas = typeof window.G_vmlCanvasManager === 'object' &&
    window.G_vmlCanvasManager !== null &&
    typeof window.G_vmlCanvasManager.initElement === 'function';

  if (useExcanvas) Chart.defaults.global.animation = false;

  return angular.module('chart.js', [])
    .provider('ChartJs', ChartJsProvider)
    .factory('ChartJsFactory', ['ChartJs', '$timeout', ChartJsFactory])
    .directive('chartBase', ['ChartJsFactory', function (ChartJsFactory) { return new ChartJsFactory(); }])
    .directive('chartLine', ['ChartJsFactory', function (ChartJsFactory) { return new ChartJsFactory('line'); }])
    .directive('chartBar', ['ChartJsFactory', function (ChartJsFactory) { return new ChartJsFactory('bar'); }])
    .directive('chartHorizontalBar', ['ChartJsFactory', function (ChartJsFactory) { return new ChartJsFactory('horizontalBar'); }])
    .directive('chartRadar', ['ChartJsFactory', function (ChartJsFactory) { return new ChartJsFactory('radar'); }])
    .directive('chartDoughnut', ['ChartJsFactory', function (ChartJsFactory) { return new ChartJsFactory('doughnut'); }])
    .directive('chartPie', ['ChartJsFactory', function (ChartJsFactory) { return new ChartJsFactory('pie'); }])
    .directive('chartPolarArea', ['ChartJsFactory', function (ChartJsFactory) { return new ChartJsFactory('polarArea'); }])
    .directive('chartBubble', ['ChartJsFactory', function (ChartJsFactory) { return new ChartJsFactory('bubble'); }])
    .name;

  /**
   * Wrapper for chart.js
   * Allows configuring chart js using the provider
   *
   * angular.module('myModule', ['chart.js']).config(function(ChartJsProvider) {
   *   ChartJsProvider.setOptions({ responsive: false });
   *   ChartJsProvider.setOptions('Line', { responsive: true });
   * })))
   */
  function ChartJsProvider () {
    var options = { responsive: true };
    var ChartJs = {
      Chart: Chart,
      getOptions: function (type) {
        var typeOptions = type && options[type] || {};
        return angular.extend({}, options, typeOptions);
      }
    };

    /**
     * Allow to set global options during configuration
     */
    this.setOptions = function (type, customOptions) {
      // If no type was specified set option for the global object
      if (! customOptions) {
        customOptions = type;
        options = angular.merge(options, customOptions);
      } else {
        // Set options for the specific chart
        options[type] = angular.merge(options[type] || {}, customOptions);
      }

      angular.merge(ChartJs.Chart.defaults, options);
    };

    this.$get = function () {
      return ChartJs;
    };
  }

  function ChartJsFactory (ChartJs, $timeout) {
    return function chart (type) {
      return {
        restrict: 'CA',
        scope: {
          chartGetColor: '=?',
          chartType: '=',
          chartData: '=?',
          chartLabels: '=?',
          chartOptions: '=?',
          chartSeries: '=?',
          chartColors: '=?',
          chartClick: '=?',
          chartHover: '=?',
          chartDatasetOverride: '=?'
        },
        link: function (scope, elem/*, attrs */) {
          if (useExcanvas) window.G_vmlCanvasManager.initElement(elem[0]);

          // Order of setting "watch" matter
          scope.$watch('chartData', watchData, true);
          scope.$watch('chartSeries', watchOther, true);
          scope.$watch('chartLabels', watchOther, true);
          scope.$watch('chartOptions', watchOther, true);
          scope.$watch('chartColors', watchOther, true);
          scope.$watch('chartDatasetOverride', watchOther, true);
          scope.$watch('chartType', watchType, false);

          scope.$on('$destroy', function () {
            destroyChart(scope);
          });

          scope.$on('$resize', function () {
            if (scope.chart) scope.chart.resize();
          });

          function watchData (newVal, oldVal) {
            if (! newVal || ! newVal.length || (Array.isArray(newVal[0]) && ! newVal[0].length)) {
              destroyChart(scope);
              return;
            }
            var chartType = type || scope.chartType;
            if (! chartType) return;

            if (scope.chart && canUpdateChart(newVal, oldVal))
              return updateChart(newVal, scope);

            createChart(chartType, scope, elem);
          }

          function watchOther (newVal, oldVal) {
            if (isEmpty(newVal)) return;
            if (angular.equals(newVal, oldVal)) return;
            var chartType = type || scope.chartType;
            if (! chartType) return;

            // chart.update() doesn't work for series and labels
            // so we have to re-create the chart entirely
            createChart(chartType, scope, elem);
          }

          function watchType (newVal, oldVal) {
            if (isEmpty(newVal)) return;
            if (angular.equals(newVal, oldVal)) return;
            createChart(newVal, scope, elem);
          }
        }
      };
    };

    function createChart (type, scope, elem) {
      var options = getChartOptions(type, scope);
      if (! hasData(scope) || ! canDisplay(type, scope, elem, options)) return;

      var cvs = elem[0];
      var ctx = cvs.getContext('2d');

      scope.chartGetColor = getChartColorFn(scope);
      var data = getChartData(type, scope);
      // Destroy old chart if it exists to avoid ghost charts issue
      // https://github.com/jtblin/angular-chart.js/issues/187
      destroyChart(scope);

      scope.chart = new ChartJs.Chart(ctx, {
        type: type,
        data: data,
        options: options
      });
      scope.$emit('chart-create', scope.chart);
      bindEvents(cvs, scope);
    }

    function canUpdateChart (newVal, oldVal) {
      if (newVal && oldVal && newVal.length && oldVal.length) {
        return Array.isArray(newVal[0]) ?
        newVal.length === oldVal.length && newVal.every(function (element, index) {
          return element.length === oldVal[index].length; }) :
          oldVal.reduce(sum, 0) > 0 ? newVal.length === oldVal.length : false;
      }
      return false;
    }

    function sum (carry, val) {
      return carry + val;
    }

    function getEventHandler (scope, action, triggerOnlyOnChange) {
      var lastState = {
        point: void 0,
        points: void 0
      };
      return function (evt) {
        var atEvent = scope.chart.getElementAtEvent || scope.chart.getPointAtEvent;
        var atEvents = scope.chart.getElementsAtEvent || scope.chart.getPointsAtEvent;
        if (atEvents) {
          var points = atEvents.call(scope.chart, evt);
          var point = atEvent ? atEvent.call(scope.chart, evt)[0] : void 0;

          if (triggerOnlyOnChange === false ||
            (! angular.equals(lastState.points, points) && ! angular.equals(lastState.point, point))
          ) {
            lastState.point = point;
            lastState.points = points;
            scope[action](points, evt, point);
          }
        }
      };
    }

    function getColors (type, scope) {
      var colors = angular.copy(scope.chartColors ||
        ChartJs.getOptions(type).chartColors ||
        Chart.defaults.global.colors
      );
      var notEnoughColors = colors.length < scope.chartData.length;
      while (colors.length < scope.chartData.length) {
        colors.push(scope.chartGetColor());
      }
      // mutate colors in this case as we don't want
      // the colors to change on each refresh
      if (notEnoughColors) scope.chartColors = colors;
      return colors.map(convertColor);
    }

    function convertColor (color) {
      // Allows RGB and RGBA colors to be input as a string: e.g.: "rgb(159,204,0)", "rgba(159,204,0, 0.5)"
      if (typeof color === 'string' && color[0] === 'r') return getColor(rgbStringToRgb(color));
      // Allows hex colors to be input as a string.
      if (typeof color === 'string' && color[0] === '#') return getColor(hexToRgb(color.substr(1)));
      // Allows colors to be input as an object, bypassing getColor() entirely
      if (typeof color === 'object' && color !== null) return color;
      return getRandomColor();
    }

    function getRandomColor () {
      var color = [getRandomInt(0, 255), getRandomInt(0, 255), getRandomInt(0, 255)];
      return getColor(color);
    }

    function getColor (color) {
      var alpha = color[3] || 1;
      color = color.slice(0, 3);
      return {
        backgroundColor: rgba(color, 0.2),
        pointBackgroundColor: rgba(color, alpha),
        pointHoverBackgroundColor: rgba(color, 0.8),
        borderColor: rgba(color, alpha),
        pointBorderColor: '#fff',
        pointHoverBorderColor: rgba(color, alpha)
      };
    }

    function getRandomInt (min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    function rgba (color, alpha) {
      // rgba not supported by IE8
      return useExcanvas ? 'rgb(' + color.join(',') + ')' : 'rgba(' + color.concat(alpha).join(',') + ')';
    }

    // Credit: http://stackoverflow.com/a/11508164/1190235
    function hexToRgb (hex) {
      var bigint = parseInt(hex, 16),
        r = (bigint >> 16) & 255,
        g = (bigint >> 8) & 255,
        b = bigint & 255;

      return [r, g, b];
    }

    function rgbStringToRgb (color) {
      var match = color.match(/^rgba?\(([\d,.]+)\)$/);
      if (! match) throw new Error('Cannot parse rgb value');
      color = match[1].split(',');
      return color.map(Number);
    }

    function hasData (scope) {
      return scope.chartData && scope.chartData.length;
    }

    function getChartColorFn (scope) {
      return typeof scope.chartGetColor === 'function' ? scope.chartGetColor : getRandomColor;
    }

    function getChartData (type, scope) {
      var colors = getColors(type, scope);
      return Array.isArray(scope.chartData[0]) ?
        getDataSets(scope.chartLabels, scope.chartData, scope.chartSeries || [], colors, scope.chartDatasetOverride) :
        getData(scope.chartLabels, scope.chartData, colors, scope.chartDatasetOverride);
    }

    function getDataSets (labels, data, series, colors, datasetOverride) {
      return {
        labels: labels,
        datasets: data.map(function (item, i) {
          var dataset = angular.extend({}, colors[i], {
            label: series[i],
            data: item
          });
          if (datasetOverride && datasetOverride.length >= i) {
            angular.merge(dataset, datasetOverride[i]);
          }
          return dataset;
        })
      };
    }

    function getData (labels, data, colors, datasetOverride) {
      var dataset = {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: colors.map(function (color) {
            return color.pointBackgroundColor;
          }),
          hoverBackgroundColor: colors.map(function (color) {
            return color.backgroundColor;
          })
        }]
      };
      if (datasetOverride) {
        angular.merge(dataset.datasets[0], datasetOverride);
      }
      return dataset;
    }

    function getChartOptions (type, scope) {
      return angular.extend({}, ChartJs.getOptions(type), scope.chartOptions);
    }

    function bindEvents (cvs, scope) {
      cvs.onclick = scope.chartClick ? getEventHandler(scope, 'chartClick', false) : angular.noop;
      cvs.onmousemove = scope.chartHover ? getEventHandler(scope, 'chartHover', true) : angular.noop;
    }

    function updateChart (values, scope) {
      if (Array.isArray(scope.chartData[0])) {
        scope.chart.data.datasets.forEach(function (dataset, i) {
          dataset.data = values[i];
        });
      } else {
        scope.chart.data.datasets[0].data = values;
      }

      scope.chart.update();
      scope.$emit('chart-update', scope.chart);
    }

    function isEmpty (value) {
      return ! value ||
        (Array.isArray(value) && ! value.length) ||
        (typeof value === 'object' && ! Object.keys(value).length);
    }

    function canDisplay (type, scope, elem, options) {
      // TODO: check parent?
      if (options.responsive && elem[0].clientHeight === 0) {
        $timeout(function () {
          createChart(type, scope, elem);
        }, 50, false);
        return false;
      }
      return true;
    }

    function destroyChart(scope) {
      if(! scope.chart) return;
      scope.chart.destroy();
      scope.$emit('chart-destroy', scope.chart);
    }
  }
}));

/**
 * [chartjs-plugin-labels]{@link https://github.com/emn178/chartjs-plugin-labels}
 *
 * @version 1.1.0
 * @author Chen, Yi-Cyuan [emn178@gmail.com]
 * @copyright Chen, Yi-Cyuan 2017-2018
 * @license MIT
 */
(function () {
  'use strict';

  if (typeof Chart === 'undefined') {
    console.error('Can not find Chart object.');
    return;
  }

  if (typeof Object.assign != 'function') {
    Object.assign = function (target, varArgs) {
      if (target == null) {
        throw new TypeError('Cannot convert undefined or null to object');
      }
      var to = Object(target);
      for (var index = 1; index < arguments.length; index++) {
        var nextSource = arguments[index];
        if (nextSource != null) {
          for (var nextKey in nextSource) {
            if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
              to[nextKey] = nextSource[nextKey];
            }
          }
        }
      }
      return to;
    };
  }

  var SUPPORTED_TYPES = {};
  ['pie', 'doughnut', 'polarArea', 'bar'].forEach(function (t) {
    SUPPORTED_TYPES[t] = true;
  });

  function Label() {
    this.renderToDataset = this.renderToDataset.bind(this);
  }

  Label.prototype.setup = function (chart, options) {
    this.chart = chart;
    this.ctx = chart.ctx;
    this.args = {};
    this.barTotal = {};
    var chartOptions = chart.config.options;
    this.options = Object.assign({
      position: 'default',
      precision: 0,
      fontSize: chartOptions.defaultFontSize,
      fontColor: chartOptions.defaultFontColor,
      fontStyle: chartOptions.defaultFontStyle,
      fontFamily: chartOptions.defaultFontFamily,
      shadowOffsetX: 3,
      shadowOffsetY: 3,
      shadowColor: 'rgba(0,0,0,0.3)',
      shadowBlur: 6,
      images: [],
      outsidePadding: 2,
      textMargin: 2,
      overlap: true
    }, options);
    if (chart.config.type === 'bar') {
      this.options.position = 'default';
      this.options.arc = false;
      this.options.overlap = true;
    }
  };

  Label.prototype.render = function () {
    this.labelBounds = [];
    this.chart.data.datasets.forEach(this.renderToDataset);
  };

  Label.prototype.renderToDataset = function (dataset, index) {
    this.totalPercentage = 0;
    this.total = null;
    var arg = this.args[index];
    arg.meta.data.forEach(function (element, index) {
      this.renderToElement(dataset, arg, element, index);
    }.bind(this));
  };

  Label.prototype.renderToElement = function (dataset, arg, element, index) {
    if (!this.shouldRenderToElement(arg.meta, element)) {
      return;
    }
    this.percentage = null;
    var label = this.getLabel(dataset, element, index);
    if (!label) {
      return;
    }
    var ctx = this.ctx;
    ctx.save();
    ctx.font = Chart.helpers.fontString(this.options.fontSize, this.options.fontStyle, this.options.fontFamily);
    var renderInfo = this.getRenderInfo(element, label);
    if (!this.drawable(element, label, renderInfo)) {
      ctx.restore();
      return;
    }
    ctx.beginPath();
    ctx.fillStyle = this.getFontColor(dataset, element, index);
    this.renderLabel(label, renderInfo);
    ctx.restore();
  };

  Label.prototype.renderLabel = function (label, renderInfo) {
    return this.options.arc ? this.renderArcLabel(label, renderInfo) : this.renderBaseLabel(label, renderInfo);
  };

  Label.prototype.renderBaseLabel = function (label, position) {
    var ctx = this.ctx;
    if (typeof label === 'object') {
      ctx.drawImage(label, position.x - label.width / 2, position.y - label.height / 2, label.width, label.height);
    } else {
      ctx.save();
      ctx.textBaseline = 'top';
      ctx.textAlign = 'center';

      if (this.options.textShadow) {
        ctx.shadowOffsetX = this.options.shadowOffsetX;
        ctx.shadowOffsetY = this.options.shadowOffsetY;
        ctx.shadowColor = this.options.shadowColor;
        ctx.shadowBlur = this.options.shadowBlur;
      }

      var lines = label.split('\n');
      for (var i = 0; i < lines.length; i++) {
        var y = position.y - this.options.fontSize / 2 * lines.length + this.options.fontSize * i;
        ctx.fillText(lines[i], position.x, y);
      }
      ctx.restore();
    }
  };

  Label.prototype.renderArcLabel = function (label, renderInfo) {
    var ctx = this.ctx, radius = renderInfo.radius, view = renderInfo.view;
    ctx.save();
    ctx.translate(view.x, view.y);
    if (typeof label === 'string') {
      ctx.rotate(renderInfo.startAngle);
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      var lines = label.split('\n'), max = 0, widths = [], offset = 0;
      if (this.options.position === 'border') {
        offset = (lines.length - 1) * this.options.fontSize / 2;
      }
      for (var j = 0; j < lines.length; ++j) {
        var mertrics = ctx.measureText(lines[j]);
        if (mertrics.width > max) {
          max = mertrics.width;
        }
        widths.push(mertrics.width);
      }
      for (var j = 0; j < lines.length; ++j) {
        var line = lines[j];
        var y = (lines.length - 1 - j) * -this.options.fontSize + offset;
        ctx.save();
        var padding = (max - widths[j]) / 2;
        ctx.rotate(padding / radius);
        for (var i = 0; i < line.length; i++) {
          var char = line.charAt(i);
          mertrics = ctx.measureText(char);
          ctx.save();
          ctx.translate(0, -1 * radius);
          ctx.fillText(char, 0, y);
          ctx.restore();
          ctx.rotate(mertrics.width / radius);
        }
        ctx.restore();
      }
    } else {
      ctx.rotate((view.startAngle + Math.PI / 2 + renderInfo.endAngle) / 2);
      ctx.translate(0, -1 * radius);
      this.renderLabel(label, { x: 0, y: 0 });
    }
    ctx.restore();
  };

  Label.prototype.shouldRenderToElement = function (meta, element) {
    return !meta.hidden && !element.hidden && (
      this.options.showZero ||
      this.chart.config.type === 'polarArea' ? element._view.outerRadius !== 0 : element._view.circumference !== 0
    );
  };

  Label.prototype.getLabel = function (dataset, element, index) {
    var label;
    if (typeof this.options.render === 'function') {
      label = this.options.render({
        label: this.chart.config.data.labels[index],
        value: dataset.data[index],
        percentage: this.getPercentage(dataset, element, index),
        dataset: dataset,
        index: index
      });
    } else {
      switch (this.options.render) {
        case 'value':
          label = dataset.data[index];
          break;
        case 'label':
          label = this.chart.config.data.labels[index];
          break;
        case 'image':
          label = this.options.images[index] ? this.loadImage(this.options.images[index]) : '';
          break;
        case 'percentage':
        default:
          label = this.getPercentage(dataset, element, index) + '%';
          break;
      }
    }
    if (typeof label === 'object') {
      label = this.loadImage(label);
    } else if (label !== null && label !== undefined) {
      label = label.toString();
    }
    return label;
  };

  Label.prototype.getFontColor = function (dataset, element, index) {
    var fontColor = this.options.fontColor;
    if (typeof fontColor === 'function') {
      fontColor = fontColor({
        label: this.chart.config.data.labels[index],
        value: dataset.data[index],
        percentage: this.getPercentage(dataset, element, index),
        backgroundColor: dataset.backgroundColor[index],
        dataset: dataset,
        index: index
      });
    } else if (typeof fontColor !== 'string') {
      fontColor = fontColor[index] || this.chart.config.options.defaultFontColor;
    }
    return fontColor;
  };

  Label.prototype.getPercentage = function (dataset, element, index) {
    if (this.percentage !== null) {
      return this.percentage;
    }
    var percentage;
    if (this.chart.config.type === 'polarArea') {
      if (this.total === null) {
        this.total = 0;
        for (var i = 0;i < dataset.data.length; ++i) {
          this.total += dataset.data[i];
        }
      }
      percentage = dataset.data[index] / this.total * 100;
    } else if (this.chart.config.type === 'bar') {
      if (this.barTotal[index] === undefined) {
        this.barTotal[index] = 0;
        for (var i = 0;i < this.chart.data.datasets.length; ++i) {
          this.barTotal[index] += this.chart.data.datasets[i].data[index];
        }
      }
      percentage = dataset.data[index] / this.barTotal[index] * 100;
    } else {
      percentage = element._view.circumference / this.chart.config.options.circumference * 100;
    }
    percentage = parseFloat(percentage.toFixed(this.options.precision));
    if (!this.options.showActualPercentages) {
      if (this.chart.config.type === 'bar') {
        this.totalPercentage = this.barTotalPercentage[index] || 0;
      }
      this.totalPercentage += percentage;
      if (this.totalPercentage > 100) {
        percentage -= this.totalPercentage - 100;
        percentage = parseFloat(percentage.toFixed(this.options.precision));
      }
      if (this.chart.config.type === 'bar') {
        this.barTotalPercentage[index] = this.totalPercentage
      }
    }
    this.percentage = percentage;
    return percentage;
  };

  Label.prototype.getRenderInfo = function (element, label) {
    if (this.chart.config.type === 'bar') {
      return this.getBarRenderInfo(element, label);
    } else {
      return this.options.arc ? this.getArcRenderInfo(element, label) : this.getBaseRenderInfo(element, label);
    }
  };

  Label.prototype.getBaseRenderInfo = function (element, label) {
    if (this.options.position === 'outside' || this.options.position === 'border') {
      var renderInfo, rangeFromCentre,
        view = element._view,
        centreAngle = view.startAngle + (view.endAngle - view.startAngle) / 2,
        innerRadius = view.outerRadius / 2;
      if (this.options.position === 'border') {
        rangeFromCentre = (view.outerRadius - innerRadius) / 2 + innerRadius;
      } else if (this.options.position === 'outside') {
        rangeFromCentre = (view.outerRadius - innerRadius) + innerRadius + this.options.textMargin;
      }
      renderInfo = {
        x: view.x + (Math.cos(centreAngle) * rangeFromCentre),
        y: view.y + (Math.sin(centreAngle) * rangeFromCentre)
      };
      if (this.options.position === 'outside') {
        var offset = this.options.textMargin + this.measureLabel(label).width / 2;
        renderInfo.x += renderInfo.x < view.x ? -offset : offset;
      }
      return renderInfo;
    } else {
      return element.tooltipPosition();
    }
  };

  Label.prototype.getArcRenderInfo = function (element, label) {
    var radius, view = element._view;
    if (this.options.position === 'outside') {
      radius = view.outerRadius + this.options.fontSize + this.options.textMargin;
    } else if (this.options.position === 'border') {
      radius = (view.outerRadius / 2 + view.outerRadius) / 2;
    } else {
      radius = (view.innerRadius + view.outerRadius) / 2;
    }
    var startAngle = view.startAngle, endAngle = view.endAngle;
    var totalAngle = endAngle - startAngle;
    startAngle += Math.PI / 2;
    endAngle += Math.PI / 2;
    var mertrics = this.measureLabel(label);
    startAngle += (endAngle - (mertrics.width / radius + startAngle)) / 2;
    return {
      radius: radius,
      startAngle: startAngle,
      endAngle: endAngle,
      totalAngle: totalAngle,
      view: view
    }
  };

  Label.prototype.getBarRenderInfo = function (element, label) {
    var renderInfo = element.tooltipPosition();
    renderInfo.y -= this.measureLabel(label).height / 2 + this.options.textMargin;
    return renderInfo;
  };

  Label.prototype.drawable = function (element, label, renderInfo) {
    if (this.options.overlap) {
      return true;
    } else if (this.options.arc) {
      return renderInfo.endAngle - renderInfo.startAngle <= renderInfo.totalAngle;
    } else {
      var mertrics = this.measureLabel(label),
        left = renderInfo.x - mertrics.width / 2,
        right = renderInfo.x + mertrics.width / 2,
        top = renderInfo.y - mertrics.height / 2,
        bottom = renderInfo.y + mertrics.height / 2;
      if (this.options.renderInfo === 'outside') {
        return this.outsideInRange(left, right, top, bottom);
      } else {
        return element.inRange(left, top) && element.inRange(left, bottom) &&
          element.inRange(right, top) && element.inRange(right, bottom);
      }
    }
  };

  Label.prototype.outsideInRange = function (left, right, top, bottom) {
    var labelBounds = this.labelBounds;
    for (var i = 0;i < labelBounds.length;++i) {
      var bound = labelBounds[i];
      var potins = [
        [left, top],
        [left, bottom],
        [right, top],
        [right, bottom]
      ];
      for (var j = 0;j < potins.length;++j) {
        var x = potins[j][0];
        var y = potins[j][1];
        if (x >= bound.left && x <= bound.right && y >= bound.top && y <= bound.bottom) {
          return false;
        }
      }
      potins = [
        [bound.left, bound.top],
        [bound.left, bound.bottom],
        [bound.right, bound.top],
        [bound.right, bound.bottom]
      ];
      for (var j = 0;j < potins.length;++j) {
        var x = potins[j][0];
        var y = potins[j][1];
        if (x >= left && x <= right && y >= top && y <= bottom) {
          return false;
        }
      }
    }
    labelBounds.push({
      left: left,
      right: right,
      top: top,
      bottom: bottom
    });
    return true;
  };

  Label.prototype.measureLabel = function (label) {
    if (typeof label === 'object') {
      return { width: label.width, height: label.height };
    } else {
      var width = 0;
      var lines = label.split('\n');
      for (var i = 0; i < lines.length; ++i) {
        var result = this.ctx.measureText(lines[i]);
        if (result.width > width) {
          width = result.width;
        }
      }
      return { width: width, height: this.options.fontSize * lines.length };
    }
  };

  Label.prototype.loadImage = function (obj) {
    var image = new Image();
    image.src = obj.src;
    image.width = obj.width;
    image.height = obj.height;
    return image;
  };

  Chart.plugins.register({
    id: 'labels',
    beforeDatasetsUpdate: function (chart, options) {
      if (!SUPPORTED_TYPES[chart.config.type]) {
        return;
      }
      if (!Array.isArray(options)) {
        options = [options];
      }
      var count = options.length;
      if (!chart._labels || count !== chart._labels.length) {
        chart._labels = options.map(function () {
          return new Label();
        });
      }
      var someOutside = false, maxPadding = 0;
      for (var i = 0; i < count; ++i) {
        var label = chart._labels[i];
        label.setup(chart, options[i]);
        if (label.options.position === 'outside') {
          someOutside = true;
          var padding = label.options.fontSize * 1.5 + label.options.outsidePadding;
          if (padding > maxPadding) {
            maxPadding = padding;
          }
        }
      }
      if (someOutside) {
        chart.chartArea.top += maxPadding;
        chart.chartArea.bottom -= maxPadding;
      }
    },
    afterDatasetUpdate: function (chart, args, options) {
      if (!SUPPORTED_TYPES[chart.config.type]) {
        return;
      }
      chart._labels.forEach(function (label) {
        label.args[args.index] = args;
      });
    },
    beforeDraw: function (chart) {
      if (!SUPPORTED_TYPES[chart.config.type]) {
        return;
      }
      chart._labels.forEach(function (label) {
        label.barTotalPercentage = {};
      });
    },
    afterDatasetsDraw: function (chart) {
      if (!SUPPORTED_TYPES[chart.config.type]) {
        return;
      }
      chart._labels.forEach(function (label) {
        label.render();
      });
    }
  });
})();

/*! pdfmake v0.1.68, @license MIT, @link http://pdfmake.org */
(function webpackUniversalModuleDefinition(root, factory) {
	if(typeof exports === 'object' && typeof module === 'object')
		module.exports = factory();
	else if(typeof define === 'function' && define.amd)
		define([], factory);
	else {
		var a = factory();
		for(var i in a) (typeof exports === 'object' ? exports : root)[i] = a[i];
	}
})(typeof self !== 'undefined' ? self : this, function() {
return /******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, { enumerable: true, get: getter });
/******/ 		}
/******/ 	};
/******/
/******/ 	// define __esModule on exports
/******/ 	__webpack_require__.r = function(exports) {
/******/ 		if(typeof Symbol !== 'undefined' && Symbol.toStringTag) {
/******/ 			Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });
/******/ 		}
/******/ 		Object.defineProperty(exports, '__esModule', { value: true });
/******/ 	};
/******/
/******/ 	// create a fake namespace object
/******/ 	// mode & 1: value is a module id, require it
/******/ 	// mode & 2: merge all properties of value into the ns
/******/ 	// mode & 4: return value when already ns object
/******/ 	// mode & 8|1: behave like require
/******/ 	__webpack_require__.t = function(value, mode) {
/******/ 		if(mode & 1) value = __webpack_require__(value);
/******/ 		if(mode & 8) return value;
/******/ 		if((mode & 4) && typeof value === 'object' && value && value.__esModule) return value;
/******/ 		var ns = Object.create(null);
/******/ 		__webpack_require__.r(ns);
/******/ 		Object.defineProperty(ns, 'default', { enumerable: true, value: value });
/******/ 		if(mode & 2 && typeof value != 'string') for(var key in value) __webpack_require__.d(ns, key, function(key) { return value[key]; }.bind(null, key));
/******/ 		return ns;
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 300);
/******/ })
/************************************************************************/
/******/ ([
/* 0 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


function isString(variable) {
	return typeof variable === 'string' || variable instanceof String;
}

function isNumber(variable) {
	return typeof variable === 'number' || variable instanceof Number;
}

function isBoolean(variable) {
	return typeof variable === 'boolean';
}

function isArray(variable) {
	return Array.isArray(variable);
}

function isFunction(variable) {
	return typeof variable === 'function';
}

function isObject(variable) {
	return variable !== null && typeof variable === 'object';
}

function isNull(variable) {
	return variable === null;
}

function isUndefined(variable) {
	return variable === undefined;
}

function pack() {
	var result = {};

	for (var i = 0, l = arguments.length; i < l; i++) {
		var obj = arguments[i];

		if (obj) {
			for (var key in obj) {
				if (obj.hasOwnProperty(key)) {
					result[key] = obj[key];
				}
			}
		}
	}

	return result;
}

function offsetVector(vector, x, y) {
	switch (vector.type) {
		case 'ellipse':
		case 'rect':
			vector.x += x;
			vector.y += y;
			break;
		case 'line':
			vector.x1 += x;
			vector.x2 += x;
			vector.y1 += y;
			vector.y2 += y;
			break;
		case 'polyline':
			for (var i = 0, l = vector.points.length; i < l; i++) {
				vector.points[i].x += x;
				vector.points[i].y += y;
			}
			break;
	}
}

function fontStringify(key, val) {
	if (key === 'font') {
		return 'font';
	}
	return val;
}

function getNodeId(node) {
	if (node.id) {
		return node.id;
	}

	if (isArray(node.text)) {
		for (var i = 0, l = node.text.length; i < l; i++) {
			var n = node.text[i];
			var nodeId = getNodeId(n);
			if (nodeId) {
				return nodeId;
			}
		}
	}

	return null;
}

module.exports = {
	isString: isString,
	isNumber: isNumber,
	isBoolean: isBoolean,
	isArray: isArray,
	isFunction: isFunction,
	isObject: isObject,
	isNull: isNull,
	isUndefined: isUndefined,
	pack: pack,
	fontStringify: fontStringify,
	offsetVector: offsetVector,
	getNodeId: getNodeId
};


/***/ }),
/* 1 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var getOwnPropertyDescriptor = __webpack_require__(54).f;
var createNonEnumerableProperty = __webpack_require__(18);
var redefine = __webpack_require__(25);
var setGlobal = __webpack_require__(139);
var copyConstructorProperties = __webpack_require__(200);
var isForced = __webpack_require__(144);

/*
  options.target      - name of the target object
  options.global      - target is the global object
  options.stat        - export as static methods of target
  options.proto       - export as prototype methods of target
  options.real        - real prototype method for the `pure` version
  options.forced      - export even if the native feature is available
  options.bind        - bind methods to the target, required for the `pure` version
  options.wrap        - wrap constructors to preventing global pollution, required for the `pure` version
  options.unsafe      - use the simple assignment of property instead of delete + defineProperty
  options.sham        - add a flag to not completely full polyfills
  options.enumerable  - export as enumerable property
  options.noTargetGet - prevent calling a getter on target
*/
module.exports = function (options, source) {
  var TARGET = options.target;
  var GLOBAL = options.global;
  var STATIC = options.stat;
  var FORCED, target, key, targetProperty, sourceProperty, descriptor;
  if (GLOBAL) {
    target = global;
  } else if (STATIC) {
    target = global[TARGET] || setGlobal(TARGET, {});
  } else {
    target = (global[TARGET] || {}).prototype;
  }
  if (target) for (key in source) {
    sourceProperty = source[key];
    if (options.noTargetGet) {
      descriptor = getOwnPropertyDescriptor(target, key);
      targetProperty = descriptor && descriptor.value;
    } else targetProperty = target[key];
    FORCED = isForced(GLOBAL ? key : TARGET + (STATIC ? '.' : '#') + key, options.forced);
    // contained in target
    if (!FORCED && targetProperty !== undefined) {
      if (typeof sourceProperty === typeof targetProperty) continue;
      copyConstructorProperties(sourceProperty, targetProperty);
    }
    // add a flag to not completely full polyfills
    if (options.sham || (targetProperty && targetProperty.sham)) {
      createNonEnumerableProperty(sourceProperty, 'sham', true);
    }
    // extend global
    redefine(target, key, sourceProperty, options);
  }
};


/***/ }),
/* 2 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory) {
	if (true) {
		// CommonJS
		module.exports = exports = factory();
	}
	else {}
}(this, function () {

	/**
	 * CryptoJS core components.
	 */
	var CryptoJS = CryptoJS || (function (Math, undefined) {
	    /*
	     * Local polyfil of Object.create
	     */
	    var create = Object.create || (function () {
	        function F() {};

	        return function (obj) {
	            var subtype;

	            F.prototype = obj;

	            subtype = new F();

	            F.prototype = null;

	            return subtype;
	        };
	    }())

	    /**
	     * CryptoJS namespace.
	     */
	    var C = {};

	    /**
	     * Library namespace.
	     */
	    var C_lib = C.lib = {};

	    /**
	     * Base object for prototypal inheritance.
	     */
	    var Base = C_lib.Base = (function () {


	        return {
	            /**
	             * Creates a new object that inherits from this object.
	             *
	             * @param {Object} overrides Properties to copy into the new object.
	             *
	             * @return {Object} The new object.
	             *
	             * @static
	             *
	             * @example
	             *
	             *     var MyType = CryptoJS.lib.Base.extend({
	             *         field: 'value',
	             *
	             *         method: function () {
	             *         }
	             *     });
	             */
	            extend: function (overrides) {
	                // Spawn
	                var subtype = create(this);

	                // Augment
	                if (overrides) {
	                    subtype.mixIn(overrides);
	                }

	                // Create default initializer
	                if (!subtype.hasOwnProperty('init') || this.init === subtype.init) {
	                    subtype.init = function () {
	                        subtype.$super.init.apply(this, arguments);
	                    };
	                }

	                // Initializer's prototype is the subtype object
	                subtype.init.prototype = subtype;

	                // Reference supertype
	                subtype.$super = this;

	                return subtype;
	            },

	            /**
	             * Extends this object and runs the init method.
	             * Arguments to create() will be passed to init().
	             *
	             * @return {Object} The new object.
	             *
	             * @static
	             *
	             * @example
	             *
	             *     var instance = MyType.create();
	             */
	            create: function () {
	                var instance = this.extend();
	                instance.init.apply(instance, arguments);

	                return instance;
	            },

	            /**
	             * Initializes a newly created object.
	             * Override this method to add some logic when your objects are created.
	             *
	             * @example
	             *
	             *     var MyType = CryptoJS.lib.Base.extend({
	             *         init: function () {
	             *             // ...
	             *         }
	             *     });
	             */
	            init: function () {
	            },

	            /**
	             * Copies properties into this object.
	             *
	             * @param {Object} properties The properties to mix in.
	             *
	             * @example
	             *
	             *     MyType.mixIn({
	             *         field: 'value'
	             *     });
	             */
	            mixIn: function (properties) {
	                for (var propertyName in properties) {
	                    if (properties.hasOwnProperty(propertyName)) {
	                        this[propertyName] = properties[propertyName];
	                    }
	                }

	                // IE won't copy toString using the loop above
	                if (properties.hasOwnProperty('toString')) {
	                    this.toString = properties.toString;
	                }
	            },

	            /**
	             * Creates a copy of this object.
	             *
	             * @return {Object} The clone.
	             *
	             * @example
	             *
	             *     var clone = instance.clone();
	             */
	            clone: function () {
	                return this.init.prototype.extend(this);
	            }
	        };
	    }());

	    /**
	     * An array of 32-bit words.
	     *
	     * @property {Array} words The array of 32-bit words.
	     * @property {number} sigBytes The number of significant bytes in this word array.
	     */
	    var WordArray = C_lib.WordArray = Base.extend({
	        /**
	         * Initializes a newly created word array.
	         *
	         * @param {Array} words (Optional) An array of 32-bit words.
	         * @param {number} sigBytes (Optional) The number of significant bytes in the words.
	         *
	         * @example
	         *
	         *     var wordArray = CryptoJS.lib.WordArray.create();
	         *     var wordArray = CryptoJS.lib.WordArray.create([0x00010203, 0x04050607]);
	         *     var wordArray = CryptoJS.lib.WordArray.create([0x00010203, 0x04050607], 6);
	         */
	        init: function (words, sigBytes) {
	            words = this.words = words || [];

	            if (sigBytes != undefined) {
	                this.sigBytes = sigBytes;
	            } else {
	                this.sigBytes = words.length * 4;
	            }
	        },

	        /**
	         * Converts this word array to a string.
	         *
	         * @param {Encoder} encoder (Optional) The encoding strategy to use. Default: CryptoJS.enc.Hex
	         *
	         * @return {string} The stringified word array.
	         *
	         * @example
	         *
	         *     var string = wordArray + '';
	         *     var string = wordArray.toString();
	         *     var string = wordArray.toString(CryptoJS.enc.Utf8);
	         */
	        toString: function (encoder) {
	            return (encoder || Hex).stringify(this);
	        },

	        /**
	         * Concatenates a word array to this word array.
	         *
	         * @param {WordArray} wordArray The word array to append.
	         *
	         * @return {WordArray} This word array.
	         *
	         * @example
	         *
	         *     wordArray1.concat(wordArray2);
	         */
	        concat: function (wordArray) {
	            // Shortcuts
	            var thisWords = this.words;
	            var thatWords = wordArray.words;
	            var thisSigBytes = this.sigBytes;
	            var thatSigBytes = wordArray.sigBytes;

	            // Clamp excess bits
	            this.clamp();

	            // Concat
	            if (thisSigBytes % 4) {
	                // Copy one byte at a time
	                for (var i = 0; i < thatSigBytes; i++) {
	                    var thatByte = (thatWords[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
	                    thisWords[(thisSigBytes + i) >>> 2] |= thatByte << (24 - ((thisSigBytes + i) % 4) * 8);
	                }
	            } else {
	                // Copy one word at a time
	                for (var i = 0; i < thatSigBytes; i += 4) {
	                    thisWords[(thisSigBytes + i) >>> 2] = thatWords[i >>> 2];
	                }
	            }
	            this.sigBytes += thatSigBytes;

	            // Chainable
	            return this;
	        },

	        /**
	         * Removes insignificant bits.
	         *
	         * @example
	         *
	         *     wordArray.clamp();
	         */
	        clamp: function () {
	            // Shortcuts
	            var words = this.words;
	            var sigBytes = this.sigBytes;

	            // Clamp
	            words[sigBytes >>> 2] &= 0xffffffff << (32 - (sigBytes % 4) * 8);
	            words.length = Math.ceil(sigBytes / 4);
	        },

	        /**
	         * Creates a copy of this word array.
	         *
	         * @return {WordArray} The clone.
	         *
	         * @example
	         *
	         *     var clone = wordArray.clone();
	         */
	        clone: function () {
	            var clone = Base.clone.call(this);
	            clone.words = this.words.slice(0);

	            return clone;
	        },

	        /**
	         * Creates a word array filled with random bytes.
	         *
	         * @param {number} nBytes The number of random bytes to generate.
	         *
	         * @return {WordArray} The random word array.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var wordArray = CryptoJS.lib.WordArray.random(16);
	         */
	        random: function (nBytes) {
	            var words = [];

	            var r = (function (m_w) {
	                var m_w = m_w;
	                var m_z = 0x3ade68b1;
	                var mask = 0xffffffff;

	                return function () {
	                    m_z = (0x9069 * (m_z & 0xFFFF) + (m_z >> 0x10)) & mask;
	                    m_w = (0x4650 * (m_w & 0xFFFF) + (m_w >> 0x10)) & mask;
	                    var result = ((m_z << 0x10) + m_w) & mask;
	                    result /= 0x100000000;
	                    result += 0.5;
	                    return result * (Math.random() > .5 ? 1 : -1);
	                }
	            });

	            for (var i = 0, rcache; i < nBytes; i += 4) {
	                var _r = r((rcache || Math.random()) * 0x100000000);

	                rcache = _r() * 0x3ade67b7;
	                words.push((_r() * 0x100000000) | 0);
	            }

	            return new WordArray.init(words, nBytes);
	        }
	    });

	    /**
	     * Encoder namespace.
	     */
	    var C_enc = C.enc = {};

	    /**
	     * Hex encoding strategy.
	     */
	    var Hex = C_enc.Hex = {
	        /**
	         * Converts a word array to a hex string.
	         *
	         * @param {WordArray} wordArray The word array.
	         *
	         * @return {string} The hex string.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var hexString = CryptoJS.enc.Hex.stringify(wordArray);
	         */
	        stringify: function (wordArray) {
	            // Shortcuts
	            var words = wordArray.words;
	            var sigBytes = wordArray.sigBytes;

	            // Convert
	            var hexChars = [];
	            for (var i = 0; i < sigBytes; i++) {
	                var bite = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
	                hexChars.push((bite >>> 4).toString(16));
	                hexChars.push((bite & 0x0f).toString(16));
	            }

	            return hexChars.join('');
	        },

	        /**
	         * Converts a hex string to a word array.
	         *
	         * @param {string} hexStr The hex string.
	         *
	         * @return {WordArray} The word array.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var wordArray = CryptoJS.enc.Hex.parse(hexString);
	         */
	        parse: function (hexStr) {
	            // Shortcut
	            var hexStrLength = hexStr.length;

	            // Convert
	            var words = [];
	            for (var i = 0; i < hexStrLength; i += 2) {
	                words[i >>> 3] |= parseInt(hexStr.substr(i, 2), 16) << (24 - (i % 8) * 4);
	            }

	            return new WordArray.init(words, hexStrLength / 2);
	        }
	    };

	    /**
	     * Latin1 encoding strategy.
	     */
	    var Latin1 = C_enc.Latin1 = {
	        /**
	         * Converts a word array to a Latin1 string.
	         *
	         * @param {WordArray} wordArray The word array.
	         *
	         * @return {string} The Latin1 string.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var latin1String = CryptoJS.enc.Latin1.stringify(wordArray);
	         */
	        stringify: function (wordArray) {
	            // Shortcuts
	            var words = wordArray.words;
	            var sigBytes = wordArray.sigBytes;

	            // Convert
	            var latin1Chars = [];
	            for (var i = 0; i < sigBytes; i++) {
	                var bite = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
	                latin1Chars.push(String.fromCharCode(bite));
	            }

	            return latin1Chars.join('');
	        },

	        /**
	         * Converts a Latin1 string to a word array.
	         *
	         * @param {string} latin1Str The Latin1 string.
	         *
	         * @return {WordArray} The word array.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var wordArray = CryptoJS.enc.Latin1.parse(latin1String);
	         */
	        parse: function (latin1Str) {
	            // Shortcut
	            var latin1StrLength = latin1Str.length;

	            // Convert
	            var words = [];
	            for (var i = 0; i < latin1StrLength; i++) {
	                words[i >>> 2] |= (latin1Str.charCodeAt(i) & 0xff) << (24 - (i % 4) * 8);
	            }

	            return new WordArray.init(words, latin1StrLength);
	        }
	    };

	    /**
	     * UTF-8 encoding strategy.
	     */
	    var Utf8 = C_enc.Utf8 = {
	        /**
	         * Converts a word array to a UTF-8 string.
	         *
	         * @param {WordArray} wordArray The word array.
	         *
	         * @return {string} The UTF-8 string.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var utf8String = CryptoJS.enc.Utf8.stringify(wordArray);
	         */
	        stringify: function (wordArray) {
	            try {
	                return decodeURIComponent(escape(Latin1.stringify(wordArray)));
	            } catch (e) {
	                throw new Error('Malformed UTF-8 data');
	            }
	        },

	        /**
	         * Converts a UTF-8 string to a word array.
	         *
	         * @param {string} utf8Str The UTF-8 string.
	         *
	         * @return {WordArray} The word array.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var wordArray = CryptoJS.enc.Utf8.parse(utf8String);
	         */
	        parse: function (utf8Str) {
	            return Latin1.parse(unescape(encodeURIComponent(utf8Str)));
	        }
	    };

	    /**
	     * Abstract buffered block algorithm template.
	     *
	     * The property blockSize must be implemented in a concrete subtype.
	     *
	     * @property {number} _minBufferSize The number of blocks that should be kept unprocessed in the buffer. Default: 0
	     */
	    var BufferedBlockAlgorithm = C_lib.BufferedBlockAlgorithm = Base.extend({
	        /**
	         * Resets this block algorithm's data buffer to its initial state.
	         *
	         * @example
	         *
	         *     bufferedBlockAlgorithm.reset();
	         */
	        reset: function () {
	            // Initial values
	            this._data = new WordArray.init();
	            this._nDataBytes = 0;
	        },

	        /**
	         * Adds new data to this block algorithm's buffer.
	         *
	         * @param {WordArray|string} data The data to append. Strings are converted to a WordArray using UTF-8.
	         *
	         * @example
	         *
	         *     bufferedBlockAlgorithm._append('data');
	         *     bufferedBlockAlgorithm._append(wordArray);
	         */
	        _append: function (data) {
	            // Convert string to WordArray, else assume WordArray already
	            if (typeof data == 'string') {
	                data = Utf8.parse(data);
	            }

	            // Append
	            this._data.concat(data);
	            this._nDataBytes += data.sigBytes;
	        },

	        /**
	         * Processes available data blocks.
	         *
	         * This method invokes _doProcessBlock(offset), which must be implemented by a concrete subtype.
	         *
	         * @param {boolean} doFlush Whether all blocks and partial blocks should be processed.
	         *
	         * @return {WordArray} The processed data.
	         *
	         * @example
	         *
	         *     var processedData = bufferedBlockAlgorithm._process();
	         *     var processedData = bufferedBlockAlgorithm._process(!!'flush');
	         */
	        _process: function (doFlush) {
	            // Shortcuts
	            var data = this._data;
	            var dataWords = data.words;
	            var dataSigBytes = data.sigBytes;
	            var blockSize = this.blockSize;
	            var blockSizeBytes = blockSize * 4;

	            // Count blocks ready
	            var nBlocksReady = dataSigBytes / blockSizeBytes;
	            if (doFlush) {
	                // Round up to include partial blocks
	                nBlocksReady = Math.ceil(nBlocksReady);
	            } else {
	                // Round down to include only full blocks,
	                // less the number of blocks that must remain in the buffer
	                nBlocksReady = Math.max((nBlocksReady | 0) - this._minBufferSize, 0);
	            }

	            // Count words ready
	            var nWordsReady = nBlocksReady * blockSize;

	            // Count bytes ready
	            var nBytesReady = Math.min(nWordsReady * 4, dataSigBytes);

	            // Process blocks
	            if (nWordsReady) {
	                for (var offset = 0; offset < nWordsReady; offset += blockSize) {
	                    // Perform concrete-algorithm logic
	                    this._doProcessBlock(dataWords, offset);
	                }

	                // Remove processed words
	                var processedWords = dataWords.splice(0, nWordsReady);
	                data.sigBytes -= nBytesReady;
	            }

	            // Return processed words
	            return new WordArray.init(processedWords, nBytesReady);
	        },

	        /**
	         * Creates a copy of this object.
	         *
	         * @return {Object} The clone.
	         *
	         * @example
	         *
	         *     var clone = bufferedBlockAlgorithm.clone();
	         */
	        clone: function () {
	            var clone = Base.clone.call(this);
	            clone._data = this._data.clone();

	            return clone;
	        },

	        _minBufferSize: 0
	    });

	    /**
	     * Abstract hasher template.
	     *
	     * @property {number} blockSize The number of 32-bit words this hasher operates on. Default: 16 (512 bits)
	     */
	    var Hasher = C_lib.Hasher = BufferedBlockAlgorithm.extend({
	        /**
	         * Configuration options.
	         */
	        cfg: Base.extend(),

	        /**
	         * Initializes a newly created hasher.
	         *
	         * @param {Object} cfg (Optional) The configuration options to use for this hash computation.
	         *
	         * @example
	         *
	         *     var hasher = CryptoJS.algo.SHA256.create();
	         */
	        init: function (cfg) {
	            // Apply config defaults
	            this.cfg = this.cfg.extend(cfg);

	            // Set initial values
	            this.reset();
	        },

	        /**
	         * Resets this hasher to its initial state.
	         *
	         * @example
	         *
	         *     hasher.reset();
	         */
	        reset: function () {
	            // Reset data buffer
	            BufferedBlockAlgorithm.reset.call(this);

	            // Perform concrete-hasher logic
	            this._doReset();
	        },

	        /**
	         * Updates this hasher with a message.
	         *
	         * @param {WordArray|string} messageUpdate The message to append.
	         *
	         * @return {Hasher} This hasher.
	         *
	         * @example
	         *
	         *     hasher.update('message');
	         *     hasher.update(wordArray);
	         */
	        update: function (messageUpdate) {
	            // Append
	            this._append(messageUpdate);

	            // Update the hash
	            this._process();

	            // Chainable
	            return this;
	        },

	        /**
	         * Finalizes the hash computation.
	         * Note that the finalize operation is effectively a destructive, read-once operation.
	         *
	         * @param {WordArray|string} messageUpdate (Optional) A final message update.
	         *
	         * @return {WordArray} The hash.
	         *
	         * @example
	         *
	         *     var hash = hasher.finalize();
	         *     var hash = hasher.finalize('message');
	         *     var hash = hasher.finalize(wordArray);
	         */
	        finalize: function (messageUpdate) {
	            // Final message update
	            if (messageUpdate) {
	                this._append(messageUpdate);
	            }

	            // Perform concrete-hasher logic
	            var hash = this._doFinalize();

	            return hash;
	        },

	        blockSize: 512/32,

	        /**
	         * Creates a shortcut function to a hasher's object interface.
	         *
	         * @param {Hasher} hasher The hasher to create a helper for.
	         *
	         * @return {Function} The shortcut function.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var SHA256 = CryptoJS.lib.Hasher._createHelper(CryptoJS.algo.SHA256);
	         */
	        _createHelper: function (hasher) {
	            return function (message, cfg) {
	                return new hasher.init(cfg).finalize(message);
	            };
	        },

	        /**
	         * Creates a shortcut function to the HMAC's object interface.
	         *
	         * @param {Hasher} hasher The hasher to use in this HMAC helper.
	         *
	         * @return {Function} The shortcut function.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var HmacSHA256 = CryptoJS.lib.Hasher._createHmacHelper(CryptoJS.algo.SHA256);
	         */
	        _createHmacHelper: function (hasher) {
	            return function (message, key) {
	                return new C_algo.HMAC.init(hasher, key).finalize(message);
	            };
	        }
	    });

	    /**
	     * Algorithm namespace.
	     */
	    var C_algo = C.algo = {};

	    return C;
	}(Math));


	return CryptoJS;

}));

/***/ }),
/* 3 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(global) {var check = function (it) {
  return it && it.Math == Math && it;
};

// https://github.com/zloirock/core-js/issues/86#issuecomment-115759028
module.exports =
  // eslint-disable-next-line no-undef
  check(typeof globalThis == 'object' && globalThis) ||
  check(typeof window == 'object' && window) ||
  check(typeof self == 'object' && self) ||
  check(typeof global == 'object' && global) ||
  // eslint-disable-next-line no-new-func
  Function('return this')();

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(24)))

/***/ }),
/* 4 */
/***/ (function(module, exports) {

module.exports = function (exec) {
  try {
    return !!exec();
  } catch (error) {
    return true;
  }
};


/***/ }),
/* 5 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var NATIVE_ARRAY_BUFFER = __webpack_require__(220);
var DESCRIPTORS = __webpack_require__(11);
var global = __webpack_require__(3);
var isObject = __webpack_require__(13);
var has = __webpack_require__(14);
var classof = __webpack_require__(117);
var createNonEnumerableProperty = __webpack_require__(18);
var redefine = __webpack_require__(25);
var defineProperty = __webpack_require__(15).f;
var getPrototypeOf = __webpack_require__(60);
var setPrototypeOf = __webpack_require__(45);
var wellKnownSymbol = __webpack_require__(6);
var uid = __webpack_require__(112);

var Int8Array = global.Int8Array;
var Int8ArrayPrototype = Int8Array && Int8Array.prototype;
var Uint8ClampedArray = global.Uint8ClampedArray;
var Uint8ClampedArrayPrototype = Uint8ClampedArray && Uint8ClampedArray.prototype;
var TypedArray = Int8Array && getPrototypeOf(Int8Array);
var TypedArrayPrototype = Int8ArrayPrototype && getPrototypeOf(Int8ArrayPrototype);
var ObjectPrototype = Object.prototype;
var isPrototypeOf = ObjectPrototype.isPrototypeOf;

var TO_STRING_TAG = wellKnownSymbol('toStringTag');
var TYPED_ARRAY_TAG = uid('TYPED_ARRAY_TAG');
// Fixing native typed arrays in Opera Presto crashes the browser, see #595
var NATIVE_ARRAY_BUFFER_VIEWS = NATIVE_ARRAY_BUFFER && !!setPrototypeOf && classof(global.opera) !== 'Opera';
var TYPED_ARRAY_TAG_REQIRED = false;
var NAME;

var TypedArrayConstructorsList = {
  Int8Array: 1,
  Uint8Array: 1,
  Uint8ClampedArray: 1,
  Int16Array: 2,
  Uint16Array: 2,
  Int32Array: 4,
  Uint32Array: 4,
  Float32Array: 4,
  Float64Array: 8
};

var isView = function isView(it) {
  var klass = classof(it);
  return klass === 'DataView' || has(TypedArrayConstructorsList, klass);
};

var isTypedArray = function (it) {
  return isObject(it) && has(TypedArrayConstructorsList, classof(it));
};

var aTypedArray = function (it) {
  if (isTypedArray(it)) return it;
  throw TypeError('Target is not a typed array');
};

var aTypedArrayConstructor = function (C) {
  if (setPrototypeOf) {
    if (isPrototypeOf.call(TypedArray, C)) return C;
  } else for (var ARRAY in TypedArrayConstructorsList) if (has(TypedArrayConstructorsList, NAME)) {
    var TypedArrayConstructor = global[ARRAY];
    if (TypedArrayConstructor && (C === TypedArrayConstructor || isPrototypeOf.call(TypedArrayConstructor, C))) {
      return C;
    }
  } throw TypeError('Target is not a typed array constructor');
};

var exportTypedArrayMethod = function (KEY, property, forced) {
  if (!DESCRIPTORS) return;
  if (forced) for (var ARRAY in TypedArrayConstructorsList) {
    var TypedArrayConstructor = global[ARRAY];
    if (TypedArrayConstructor && has(TypedArrayConstructor.prototype, KEY)) {
      delete TypedArrayConstructor.prototype[KEY];
    }
  }
  if (!TypedArrayPrototype[KEY] || forced) {
    redefine(TypedArrayPrototype, KEY, forced ? property
      : NATIVE_ARRAY_BUFFER_VIEWS && Int8ArrayPrototype[KEY] || property);
  }
};

var exportTypedArrayStaticMethod = function (KEY, property, forced) {
  var ARRAY, TypedArrayConstructor;
  if (!DESCRIPTORS) return;
  if (setPrototypeOf) {
    if (forced) for (ARRAY in TypedArrayConstructorsList) {
      TypedArrayConstructor = global[ARRAY];
      if (TypedArrayConstructor && has(TypedArrayConstructor, KEY)) {
        delete TypedArrayConstructor[KEY];
      }
    }
    if (!TypedArray[KEY] || forced) {
      // V8 ~ Chrome 49-50 `%TypedArray%` methods are non-writable non-configurable
      try {
        return redefine(TypedArray, KEY, forced ? property : NATIVE_ARRAY_BUFFER_VIEWS && Int8Array[KEY] || property);
      } catch (error) { /* empty */ }
    } else return;
  }
  for (ARRAY in TypedArrayConstructorsList) {
    TypedArrayConstructor = global[ARRAY];
    if (TypedArrayConstructor && (!TypedArrayConstructor[KEY] || forced)) {
      redefine(TypedArrayConstructor, KEY, property);
    }
  }
};

for (NAME in TypedArrayConstructorsList) {
  if (!global[NAME]) NATIVE_ARRAY_BUFFER_VIEWS = false;
}

// WebKit bug - typed arrays constructors prototype is Object.prototype
if (!NATIVE_ARRAY_BUFFER_VIEWS || typeof TypedArray != 'function' || TypedArray === Function.prototype) {
  // eslint-disable-next-line no-shadow
  TypedArray = function TypedArray() {
    throw TypeError('Incorrect invocation');
  };
  if (NATIVE_ARRAY_BUFFER_VIEWS) for (NAME in TypedArrayConstructorsList) {
    if (global[NAME]) setPrototypeOf(global[NAME], TypedArray);
  }
}

if (!NATIVE_ARRAY_BUFFER_VIEWS || !TypedArrayPrototype || TypedArrayPrototype === ObjectPrototype) {
  TypedArrayPrototype = TypedArray.prototype;
  if (NATIVE_ARRAY_BUFFER_VIEWS) for (NAME in TypedArrayConstructorsList) {
    if (global[NAME]) setPrototypeOf(global[NAME].prototype, TypedArrayPrototype);
  }
}

// WebKit bug - one more object in Uint8ClampedArray prototype chain
if (NATIVE_ARRAY_BUFFER_VIEWS && getPrototypeOf(Uint8ClampedArrayPrototype) !== TypedArrayPrototype) {
  setPrototypeOf(Uint8ClampedArrayPrototype, TypedArrayPrototype);
}

if (DESCRIPTORS && !has(TypedArrayPrototype, TO_STRING_TAG)) {
  TYPED_ARRAY_TAG_REQIRED = true;
  defineProperty(TypedArrayPrototype, TO_STRING_TAG, { get: function () {
    return isObject(this) ? this[TYPED_ARRAY_TAG] : undefined;
  } });
  for (NAME in TypedArrayConstructorsList) if (global[NAME]) {
    createNonEnumerableProperty(global[NAME], TYPED_ARRAY_TAG, NAME);
  }
}

module.exports = {
  NATIVE_ARRAY_BUFFER_VIEWS: NATIVE_ARRAY_BUFFER_VIEWS,
  TYPED_ARRAY_TAG: TYPED_ARRAY_TAG_REQIRED && TYPED_ARRAY_TAG,
  aTypedArray: aTypedArray,
  aTypedArrayConstructor: aTypedArrayConstructor,
  exportTypedArrayMethod: exportTypedArrayMethod,
  exportTypedArrayStaticMethod: exportTypedArrayStaticMethod,
  isView: isView,
  isTypedArray: isTypedArray,
  TypedArray: TypedArray,
  TypedArrayPrototype: TypedArrayPrototype
};


/***/ }),
/* 6 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var shared = __webpack_require__(141);
var has = __webpack_require__(14);
var uid = __webpack_require__(112);
var NATIVE_SYMBOL = __webpack_require__(145);
var USE_SYMBOL_AS_UID = __webpack_require__(202);

var WellKnownSymbolsStore = shared('wks');
var Symbol = global.Symbol;
var createWellKnownSymbol = USE_SYMBOL_AS_UID ? Symbol : Symbol && Symbol.withoutSetter || uid;

module.exports = function (name) {
  if (!has(WellKnownSymbolsStore, name)) {
    if (NATIVE_SYMBOL && has(Symbol, name)) WellKnownSymbolsStore[name] = Symbol[name];
    else WellKnownSymbolsStore[name] = createWellKnownSymbol('Symbol.' + name);
  } return WellKnownSymbolsStore[name];
};


/***/ }),
/* 7 */
/***/ (function(module, exports) {

var core = module.exports = { version: '2.6.11' };
if (typeof __e == 'number') __e = core; // eslint-disable-line no-undef


/***/ }),
/* 8 */
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__(37);

var min = Math.min;

// `ToLength` abstract operation
// https://tc39.github.io/ecma262/#sec-tolength
module.exports = function (argument) {
  return argument > 0 ? min(toInteger(argument), 0x1FFFFFFFFFFFFF) : 0; // 2 ** 53 - 1 == 9007199254740991
};


/***/ }),
/* 9 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(17);
var core = __webpack_require__(7);
var ctx = __webpack_require__(33);
var hide = __webpack_require__(41);
var has = __webpack_require__(51);
var PROTOTYPE = 'prototype';

var $export = function (type, name, source) {
  var IS_FORCED = type & $export.F;
  var IS_GLOBAL = type & $export.G;
  var IS_STATIC = type & $export.S;
  var IS_PROTO = type & $export.P;
  var IS_BIND = type & $export.B;
  var IS_WRAP = type & $export.W;
  var exports = IS_GLOBAL ? core : core[name] || (core[name] = {});
  var expProto = exports[PROTOTYPE];
  var target = IS_GLOBAL ? global : IS_STATIC ? global[name] : (global[name] || {})[PROTOTYPE];
  var key, own, out;
  if (IS_GLOBAL) source = name;
  for (key in source) {
    // contains in native
    own = !IS_FORCED && target && target[key] !== undefined;
    if (own && has(exports, key)) continue;
    // export native or passed
    out = own ? target[key] : source[key];
    // prevent global pollution for namespaces
    exports[key] = IS_GLOBAL && typeof target[key] != 'function' ? source[key]
    // bind timers to global for call from export context
    : IS_BIND && own ? ctx(out, global)
    // wrap global constructors for prevent change them in library
    : IS_WRAP && target[key] == out ? (function (C) {
      var F = function (a, b, c) {
        if (this instanceof C) {
          switch (arguments.length) {
            case 0: return new C();
            case 1: return new C(a);
            case 2: return new C(a, b);
          } return new C(a, b, c);
        } return C.apply(this, arguments);
      };
      F[PROTOTYPE] = C[PROTOTYPE];
      return F;
    // make static versions for prototype methods
    })(out) : IS_PROTO && typeof out == 'function' ? ctx(Function.call, out) : out;
    // export proto methods to core.%CONSTRUCTOR%.methods.%NAME%
    if (IS_PROTO) {
      (exports.virtual || (exports.virtual = {}))[key] = out;
      // export proto methods to core.%CONSTRUCTOR%.prototype.%NAME%
      if (type & $export.R && expProto && !expProto[key]) hide(expProto, key, out);
    }
  }
};
// type bitmap
$export.F = 1;   // forced
$export.G = 2;   // global
$export.S = 4;   // static
$export.P = 8;   // proto
$export.B = 16;  // bind
$export.W = 32;  // wrap
$export.U = 64;  // safe
$export.R = 128; // real proto method for `library`
module.exports = $export;


/***/ }),
/* 10 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(global) {/*!
 * The buffer module from node.js, for the browser.
 *
 * @author   Feross Aboukhadijeh <http://feross.org>
 * @license  MIT
 */
/* eslint-disable no-proto */



var base64 = __webpack_require__(302)
var ieee754 = __webpack_require__(303)
var isArray = __webpack_require__(196)

exports.Buffer = Buffer
exports.SlowBuffer = SlowBuffer
exports.INSPECT_MAX_BYTES = 50

/**
 * If `Buffer.TYPED_ARRAY_SUPPORT`:
 *   === true    Use Uint8Array implementation (fastest)
 *   === false   Use Object implementation (most compatible, even IE6)
 *
 * Browsers that support typed arrays are IE 10+, Firefox 4+, Chrome 7+, Safari 5.1+,
 * Opera 11.6+, iOS 4.2+.
 *
 * Due to various browser bugs, sometimes the Object implementation will be used even
 * when the browser supports typed arrays.
 *
 * Note:
 *
 *   - Firefox 4-29 lacks support for adding new properties to `Uint8Array` instances,
 *     See: https://bugzilla.mozilla.org/show_bug.cgi?id=695438.
 *
 *   - Chrome 9-10 is missing the `TypedArray.prototype.subarray` function.
 *
 *   - IE10 has a broken `TypedArray.prototype.subarray` function which returns arrays of
 *     incorrect length in some situations.

 * We detect these buggy browsers and set `Buffer.TYPED_ARRAY_SUPPORT` to `false` so they
 * get the Object implementation, which is slower but behaves correctly.
 */
Buffer.TYPED_ARRAY_SUPPORT = global.TYPED_ARRAY_SUPPORT !== undefined
  ? global.TYPED_ARRAY_SUPPORT
  : typedArraySupport()

/*
 * Export kMaxLength after typed array support is determined.
 */
exports.kMaxLength = kMaxLength()

function typedArraySupport () {
  try {
    var arr = new Uint8Array(1)
    arr.__proto__ = {__proto__: Uint8Array.prototype, foo: function () { return 42 }}
    return arr.foo() === 42 && // typed array instances can be augmented
        typeof arr.subarray === 'function' && // chrome 9-10 lack `subarray`
        arr.subarray(1, 1).byteLength === 0 // ie10 has broken `subarray`
  } catch (e) {
    return false
  }
}

function kMaxLength () {
  return Buffer.TYPED_ARRAY_SUPPORT
    ? 0x7fffffff
    : 0x3fffffff
}

function createBuffer (that, length) {
  if (kMaxLength() < length) {
    throw new RangeError('Invalid typed array length')
  }
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    // Return an augmented `Uint8Array` instance, for best performance
    that = new Uint8Array(length)
    that.__proto__ = Buffer.prototype
  } else {
    // Fallback: Return an object instance of the Buffer class
    if (that === null) {
      that = new Buffer(length)
    }
    that.length = length
  }

  return that
}

/**
 * The Buffer constructor returns instances of `Uint8Array` that have their
 * prototype changed to `Buffer.prototype`. Furthermore, `Buffer` is a subclass of
 * `Uint8Array`, so the returned instances will have all the node `Buffer` methods
 * and the `Uint8Array` methods. Square bracket notation works as expected -- it
 * returns a single octet.
 *
 * The `Uint8Array` prototype remains unmodified.
 */

function Buffer (arg, encodingOrOffset, length) {
  if (!Buffer.TYPED_ARRAY_SUPPORT && !(this instanceof Buffer)) {
    return new Buffer(arg, encodingOrOffset, length)
  }

  // Common case.
  if (typeof arg === 'number') {
    if (typeof encodingOrOffset === 'string') {
      throw new Error(
        'If encoding is specified then the first argument must be a string'
      )
    }
    return allocUnsafe(this, arg)
  }
  return from(this, arg, encodingOrOffset, length)
}

Buffer.poolSize = 8192 // not used by this implementation

// TODO: Legacy, not needed anymore. Remove in next major version.
Buffer._augment = function (arr) {
  arr.__proto__ = Buffer.prototype
  return arr
}

function from (that, value, encodingOrOffset, length) {
  if (typeof value === 'number') {
    throw new TypeError('"value" argument must not be a number')
  }

  if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) {
    return fromArrayBuffer(that, value, encodingOrOffset, length)
  }

  if (typeof value === 'string') {
    return fromString(that, value, encodingOrOffset)
  }

  return fromObject(that, value)
}

/**
 * Functionally equivalent to Buffer(arg, encoding) but throws a TypeError
 * if value is a number.
 * Buffer.from(str[, encoding])
 * Buffer.from(array)
 * Buffer.from(buffer)
 * Buffer.from(arrayBuffer[, byteOffset[, length]])
 **/
Buffer.from = function (value, encodingOrOffset, length) {
  return from(null, value, encodingOrOffset, length)
}

if (Buffer.TYPED_ARRAY_SUPPORT) {
  Buffer.prototype.__proto__ = Uint8Array.prototype
  Buffer.__proto__ = Uint8Array
  if (typeof Symbol !== 'undefined' && Symbol.species &&
      Buffer[Symbol.species] === Buffer) {
    // Fix subarray() in ES2016. See: https://github.com/feross/buffer/pull/97
    Object.defineProperty(Buffer, Symbol.species, {
      value: null,
      configurable: true
    })
  }
}

function assertSize (size) {
  if (typeof size !== 'number') {
    throw new TypeError('"size" argument must be a number')
  } else if (size < 0) {
    throw new RangeError('"size" argument must not be negative')
  }
}

function alloc (that, size, fill, encoding) {
  assertSize(size)
  if (size <= 0) {
    return createBuffer(that, size)
  }
  if (fill !== undefined) {
    // Only pay attention to encoding if it's a string. This
    // prevents accidentally sending in a number that would
    // be interpretted as a start offset.
    return typeof encoding === 'string'
      ? createBuffer(that, size).fill(fill, encoding)
      : createBuffer(that, size).fill(fill)
  }
  return createBuffer(that, size)
}

/**
 * Creates a new filled Buffer instance.
 * alloc(size[, fill[, encoding]])
 **/
Buffer.alloc = function (size, fill, encoding) {
  return alloc(null, size, fill, encoding)
}

function allocUnsafe (that, size) {
  assertSize(size)
  that = createBuffer(that, size < 0 ? 0 : checked(size) | 0)
  if (!Buffer.TYPED_ARRAY_SUPPORT) {
    for (var i = 0; i < size; ++i) {
      that[i] = 0
    }
  }
  return that
}

/**
 * Equivalent to Buffer(num), by default creates a non-zero-filled Buffer instance.
 * */
Buffer.allocUnsafe = function (size) {
  return allocUnsafe(null, size)
}
/**
 * Equivalent to SlowBuffer(num), by default creates a non-zero-filled Buffer instance.
 */
Buffer.allocUnsafeSlow = function (size) {
  return allocUnsafe(null, size)
}

function fromString (that, string, encoding) {
  if (typeof encoding !== 'string' || encoding === '') {
    encoding = 'utf8'
  }

  if (!Buffer.isEncoding(encoding)) {
    throw new TypeError('"encoding" must be a valid string encoding')
  }

  var length = byteLength(string, encoding) | 0
  that = createBuffer(that, length)

  var actual = that.write(string, encoding)

  if (actual !== length) {
    // Writing a hex string, for example, that contains invalid characters will
    // cause everything after the first invalid character to be ignored. (e.g.
    // 'abxxcd' will be treated as 'ab')
    that = that.slice(0, actual)
  }

  return that
}

function fromArrayLike (that, array) {
  var length = array.length < 0 ? 0 : checked(array.length) | 0
  that = createBuffer(that, length)
  for (var i = 0; i < length; i += 1) {
    that[i] = array[i] & 255
  }
  return that
}

function fromArrayBuffer (that, array, byteOffset, length) {
  array.byteLength // this throws if `array` is not a valid ArrayBuffer

  if (byteOffset < 0 || array.byteLength < byteOffset) {
    throw new RangeError('\'offset\' is out of bounds')
  }

  if (array.byteLength < byteOffset + (length || 0)) {
    throw new RangeError('\'length\' is out of bounds')
  }

  if (byteOffset === undefined && length === undefined) {
    array = new Uint8Array(array)
  } else if (length === undefined) {
    array = new Uint8Array(array, byteOffset)
  } else {
    array = new Uint8Array(array, byteOffset, length)
  }

  if (Buffer.TYPED_ARRAY_SUPPORT) {
    // Return an augmented `Uint8Array` instance, for best performance
    that = array
    that.__proto__ = Buffer.prototype
  } else {
    // Fallback: Return an object instance of the Buffer class
    that = fromArrayLike(that, array)
  }
  return that
}

function fromObject (that, obj) {
  if (Buffer.isBuffer(obj)) {
    var len = checked(obj.length) | 0
    that = createBuffer(that, len)

    if (that.length === 0) {
      return that
    }

    obj.copy(that, 0, 0, len)
    return that
  }

  if (obj) {
    if ((typeof ArrayBuffer !== 'undefined' &&
        obj.buffer instanceof ArrayBuffer) || 'length' in obj) {
      if (typeof obj.length !== 'number' || isnan(obj.length)) {
        return createBuffer(that, 0)
      }
      return fromArrayLike(that, obj)
    }

    if (obj.type === 'Buffer' && isArray(obj.data)) {
      return fromArrayLike(that, obj.data)
    }
  }

  throw new TypeError('First argument must be a string, Buffer, ArrayBuffer, Array, or array-like object.')
}

function checked (length) {
  // Note: cannot use `length < kMaxLength()` here because that fails when
  // length is NaN (which is otherwise coerced to zero.)
  if (length >= kMaxLength()) {
    throw new RangeError('Attempt to allocate Buffer larger than maximum ' +
                         'size: 0x' + kMaxLength().toString(16) + ' bytes')
  }
  return length | 0
}

function SlowBuffer (length) {
  if (+length != length) { // eslint-disable-line eqeqeq
    length = 0
  }
  return Buffer.alloc(+length)
}

Buffer.isBuffer = function isBuffer (b) {
  return !!(b != null && b._isBuffer)
}

Buffer.compare = function compare (a, b) {
  if (!Buffer.isBuffer(a) || !Buffer.isBuffer(b)) {
    throw new TypeError('Arguments must be Buffers')
  }

  if (a === b) return 0

  var x = a.length
  var y = b.length

  for (var i = 0, len = Math.min(x, y); i < len; ++i) {
    if (a[i] !== b[i]) {
      x = a[i]
      y = b[i]
      break
    }
  }

  if (x < y) return -1
  if (y < x) return 1
  return 0
}

Buffer.isEncoding = function isEncoding (encoding) {
  switch (String(encoding).toLowerCase()) {
    case 'hex':
    case 'utf8':
    case 'utf-8':
    case 'ascii':
    case 'latin1':
    case 'binary':
    case 'base64':
    case 'ucs2':
    case 'ucs-2':
    case 'utf16le':
    case 'utf-16le':
      return true
    default:
      return false
  }
}

Buffer.concat = function concat (list, length) {
  if (!isArray(list)) {
    throw new TypeError('"list" argument must be an Array of Buffers')
  }

  if (list.length === 0) {
    return Buffer.alloc(0)
  }

  var i
  if (length === undefined) {
    length = 0
    for (i = 0; i < list.length; ++i) {
      length += list[i].length
    }
  }

  var buffer = Buffer.allocUnsafe(length)
  var pos = 0
  for (i = 0; i < list.length; ++i) {
    var buf = list[i]
    if (!Buffer.isBuffer(buf)) {
      throw new TypeError('"list" argument must be an Array of Buffers')
    }
    buf.copy(buffer, pos)
    pos += buf.length
  }
  return buffer
}

function byteLength (string, encoding) {
  if (Buffer.isBuffer(string)) {
    return string.length
  }
  if (typeof ArrayBuffer !== 'undefined' && typeof ArrayBuffer.isView === 'function' &&
      (ArrayBuffer.isView(string) || string instanceof ArrayBuffer)) {
    return string.byteLength
  }
  if (typeof string !== 'string') {
    string = '' + string
  }

  var len = string.length
  if (len === 0) return 0

  // Use a for loop to avoid recursion
  var loweredCase = false
  for (;;) {
    switch (encoding) {
      case 'ascii':
      case 'latin1':
      case 'binary':
        return len
      case 'utf8':
      case 'utf-8':
      case undefined:
        return utf8ToBytes(string).length
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return len * 2
      case 'hex':
        return len >>> 1
      case 'base64':
        return base64ToBytes(string).length
      default:
        if (loweredCase) return utf8ToBytes(string).length // assume utf8
        encoding = ('' + encoding).toLowerCase()
        loweredCase = true
    }
  }
}
Buffer.byteLength = byteLength

function slowToString (encoding, start, end) {
  var loweredCase = false

  // No need to verify that "this.length <= MAX_UINT32" since it's a read-only
  // property of a typed array.

  // This behaves neither like String nor Uint8Array in that we set start/end
  // to their upper/lower bounds if the value passed is out of range.
  // undefined is handled specially as per ECMA-262 6th Edition,
  // Section 13.3.3.7 Runtime Semantics: KeyedBindingInitialization.
  if (start === undefined || start < 0) {
    start = 0
  }
  // Return early if start > this.length. Done here to prevent potential uint32
  // coercion fail below.
  if (start > this.length) {
    return ''
  }

  if (end === undefined || end > this.length) {
    end = this.length
  }

  if (end <= 0) {
    return ''
  }

  // Force coersion to uint32. This will also coerce falsey/NaN values to 0.
  end >>>= 0
  start >>>= 0

  if (end <= start) {
    return ''
  }

  if (!encoding) encoding = 'utf8'

  while (true) {
    switch (encoding) {
      case 'hex':
        return hexSlice(this, start, end)

      case 'utf8':
      case 'utf-8':
        return utf8Slice(this, start, end)

      case 'ascii':
        return asciiSlice(this, start, end)

      case 'latin1':
      case 'binary':
        return latin1Slice(this, start, end)

      case 'base64':
        return base64Slice(this, start, end)

      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return utf16leSlice(this, start, end)

      default:
        if (loweredCase) throw new TypeError('Unknown encoding: ' + encoding)
        encoding = (encoding + '').toLowerCase()
        loweredCase = true
    }
  }
}

// The property is used by `Buffer.isBuffer` and `is-buffer` (in Safari 5-7) to detect
// Buffer instances.
Buffer.prototype._isBuffer = true

function swap (b, n, m) {
  var i = b[n]
  b[n] = b[m]
  b[m] = i
}

Buffer.prototype.swap16 = function swap16 () {
  var len = this.length
  if (len % 2 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 16-bits')
  }
  for (var i = 0; i < len; i += 2) {
    swap(this, i, i + 1)
  }
  return this
}

Buffer.prototype.swap32 = function swap32 () {
  var len = this.length
  if (len % 4 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 32-bits')
  }
  for (var i = 0; i < len; i += 4) {
    swap(this, i, i + 3)
    swap(this, i + 1, i + 2)
  }
  return this
}

Buffer.prototype.swap64 = function swap64 () {
  var len = this.length
  if (len % 8 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 64-bits')
  }
  for (var i = 0; i < len; i += 8) {
    swap(this, i, i + 7)
    swap(this, i + 1, i + 6)
    swap(this, i + 2, i + 5)
    swap(this, i + 3, i + 4)
  }
  return this
}

Buffer.prototype.toString = function toString () {
  var length = this.length | 0
  if (length === 0) return ''
  if (arguments.length === 0) return utf8Slice(this, 0, length)
  return slowToString.apply(this, arguments)
}

Buffer.prototype.equals = function equals (b) {
  if (!Buffer.isBuffer(b)) throw new TypeError('Argument must be a Buffer')
  if (this === b) return true
  return Buffer.compare(this, b) === 0
}

Buffer.prototype.inspect = function inspect () {
  var str = ''
  var max = exports.INSPECT_MAX_BYTES
  if (this.length > 0) {
    str = this.toString('hex', 0, max).match(/.{2}/g).join(' ')
    if (this.length > max) str += ' ... '
  }
  return '<Buffer ' + str + '>'
}

Buffer.prototype.compare = function compare (target, start, end, thisStart, thisEnd) {
  if (!Buffer.isBuffer(target)) {
    throw new TypeError('Argument must be a Buffer')
  }

  if (start === undefined) {
    start = 0
  }
  if (end === undefined) {
    end = target ? target.length : 0
  }
  if (thisStart === undefined) {
    thisStart = 0
  }
  if (thisEnd === undefined) {
    thisEnd = this.length
  }

  if (start < 0 || end > target.length || thisStart < 0 || thisEnd > this.length) {
    throw new RangeError('out of range index')
  }

  if (thisStart >= thisEnd && start >= end) {
    return 0
  }
  if (thisStart >= thisEnd) {
    return -1
  }
  if (start >= end) {
    return 1
  }

  start >>>= 0
  end >>>= 0
  thisStart >>>= 0
  thisEnd >>>= 0

  if (this === target) return 0

  var x = thisEnd - thisStart
  var y = end - start
  var len = Math.min(x, y)

  var thisCopy = this.slice(thisStart, thisEnd)
  var targetCopy = target.slice(start, end)

  for (var i = 0; i < len; ++i) {
    if (thisCopy[i] !== targetCopy[i]) {
      x = thisCopy[i]
      y = targetCopy[i]
      break
    }
  }

  if (x < y) return -1
  if (y < x) return 1
  return 0
}

// Finds either the first index of `val` in `buffer` at offset >= `byteOffset`,
// OR the last index of `val` in `buffer` at offset <= `byteOffset`.
//
// Arguments:
// - buffer - a Buffer to search
// - val - a string, Buffer, or number
// - byteOffset - an index into `buffer`; will be clamped to an int32
// - encoding - an optional encoding, relevant is val is a string
// - dir - true for indexOf, false for lastIndexOf
function bidirectionalIndexOf (buffer, val, byteOffset, encoding, dir) {
  // Empty buffer means no match
  if (buffer.length === 0) return -1

  // Normalize byteOffset
  if (typeof byteOffset === 'string') {
    encoding = byteOffset
    byteOffset = 0
  } else if (byteOffset > 0x7fffffff) {
    byteOffset = 0x7fffffff
  } else if (byteOffset < -0x80000000) {
    byteOffset = -0x80000000
  }
  byteOffset = +byteOffset  // Coerce to Number.
  if (isNaN(byteOffset)) {
    // byteOffset: it it's undefined, null, NaN, "foo", etc, search whole buffer
    byteOffset = dir ? 0 : (buffer.length - 1)
  }

  // Normalize byteOffset: negative offsets start from the end of the buffer
  if (byteOffset < 0) byteOffset = buffer.length + byteOffset
  if (byteOffset >= buffer.length) {
    if (dir) return -1
    else byteOffset = buffer.length - 1
  } else if (byteOffset < 0) {
    if (dir) byteOffset = 0
    else return -1
  }

  // Normalize val
  if (typeof val === 'string') {
    val = Buffer.from(val, encoding)
  }

  // Finally, search either indexOf (if dir is true) or lastIndexOf
  if (Buffer.isBuffer(val)) {
    // Special case: looking for empty string/buffer always fails
    if (val.length === 0) {
      return -1
    }
    return arrayIndexOf(buffer, val, byteOffset, encoding, dir)
  } else if (typeof val === 'number') {
    val = val & 0xFF // Search for a byte value [0-255]
    if (Buffer.TYPED_ARRAY_SUPPORT &&
        typeof Uint8Array.prototype.indexOf === 'function') {
      if (dir) {
        return Uint8Array.prototype.indexOf.call(buffer, val, byteOffset)
      } else {
        return Uint8Array.prototype.lastIndexOf.call(buffer, val, byteOffset)
      }
    }
    return arrayIndexOf(buffer, [ val ], byteOffset, encoding, dir)
  }

  throw new TypeError('val must be string, number or Buffer')
}

function arrayIndexOf (arr, val, byteOffset, encoding, dir) {
  var indexSize = 1
  var arrLength = arr.length
  var valLength = val.length

  if (encoding !== undefined) {
    encoding = String(encoding).toLowerCase()
    if (encoding === 'ucs2' || encoding === 'ucs-2' ||
        encoding === 'utf16le' || encoding === 'utf-16le') {
      if (arr.length < 2 || val.length < 2) {
        return -1
      }
      indexSize = 2
      arrLength /= 2
      valLength /= 2
      byteOffset /= 2
    }
  }

  function read (buf, i) {
    if (indexSize === 1) {
      return buf[i]
    } else {
      return buf.readUInt16BE(i * indexSize)
    }
  }

  var i
  if (dir) {
    var foundIndex = -1
    for (i = byteOffset; i < arrLength; i++) {
      if (read(arr, i) === read(val, foundIndex === -1 ? 0 : i - foundIndex)) {
        if (foundIndex === -1) foundIndex = i
        if (i - foundIndex + 1 === valLength) return foundIndex * indexSize
      } else {
        if (foundIndex !== -1) i -= i - foundIndex
        foundIndex = -1
      }
    }
  } else {
    if (byteOffset + valLength > arrLength) byteOffset = arrLength - valLength
    for (i = byteOffset; i >= 0; i--) {
      var found = true
      for (var j = 0; j < valLength; j++) {
        if (read(arr, i + j) !== read(val, j)) {
          found = false
          break
        }
      }
      if (found) return i
    }
  }

  return -1
}

Buffer.prototype.includes = function includes (val, byteOffset, encoding) {
  return this.indexOf(val, byteOffset, encoding) !== -1
}

Buffer.prototype.indexOf = function indexOf (val, byteOffset, encoding) {
  return bidirectionalIndexOf(this, val, byteOffset, encoding, true)
}

Buffer.prototype.lastIndexOf = function lastIndexOf (val, byteOffset, encoding) {
  return bidirectionalIndexOf(this, val, byteOffset, encoding, false)
}

function hexWrite (buf, string, offset, length) {
  offset = Number(offset) || 0
  var remaining = buf.length - offset
  if (!length) {
    length = remaining
  } else {
    length = Number(length)
    if (length > remaining) {
      length = remaining
    }
  }

  // must be an even number of digits
  var strLen = string.length
  if (strLen % 2 !== 0) throw new TypeError('Invalid hex string')

  if (length > strLen / 2) {
    length = strLen / 2
  }
  for (var i = 0; i < length; ++i) {
    var parsed = parseInt(string.substr(i * 2, 2), 16)
    if (isNaN(parsed)) return i
    buf[offset + i] = parsed
  }
  return i
}

function utf8Write (buf, string, offset, length) {
  return blitBuffer(utf8ToBytes(string, buf.length - offset), buf, offset, length)
}

function asciiWrite (buf, string, offset, length) {
  return blitBuffer(asciiToBytes(string), buf, offset, length)
}

function latin1Write (buf, string, offset, length) {
  return asciiWrite(buf, string, offset, length)
}

function base64Write (buf, string, offset, length) {
  return blitBuffer(base64ToBytes(string), buf, offset, length)
}

function ucs2Write (buf, string, offset, length) {
  return blitBuffer(utf16leToBytes(string, buf.length - offset), buf, offset, length)
}

Buffer.prototype.write = function write (string, offset, length, encoding) {
  // Buffer#write(string)
  if (offset === undefined) {
    encoding = 'utf8'
    length = this.length
    offset = 0
  // Buffer#write(string, encoding)
  } else if (length === undefined && typeof offset === 'string') {
    encoding = offset
    length = this.length
    offset = 0
  // Buffer#write(string, offset[, length][, encoding])
  } else if (isFinite(offset)) {
    offset = offset | 0
    if (isFinite(length)) {
      length = length | 0
      if (encoding === undefined) encoding = 'utf8'
    } else {
      encoding = length
      length = undefined
    }
  // legacy write(string, encoding, offset, length) - remove in v0.13
  } else {
    throw new Error(
      'Buffer.write(string, encoding, offset[, length]) is no longer supported'
    )
  }

  var remaining = this.length - offset
  if (length === undefined || length > remaining) length = remaining

  if ((string.length > 0 && (length < 0 || offset < 0)) || offset > this.length) {
    throw new RangeError('Attempt to write outside buffer bounds')
  }

  if (!encoding) encoding = 'utf8'

  var loweredCase = false
  for (;;) {
    switch (encoding) {
      case 'hex':
        return hexWrite(this, string, offset, length)

      case 'utf8':
      case 'utf-8':
        return utf8Write(this, string, offset, length)

      case 'ascii':
        return asciiWrite(this, string, offset, length)

      case 'latin1':
      case 'binary':
        return latin1Write(this, string, offset, length)

      case 'base64':
        // Warning: maxLength not taken into account in base64Write
        return base64Write(this, string, offset, length)

      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return ucs2Write(this, string, offset, length)

      default:
        if (loweredCase) throw new TypeError('Unknown encoding: ' + encoding)
        encoding = ('' + encoding).toLowerCase()
        loweredCase = true
    }
  }
}

Buffer.prototype.toJSON = function toJSON () {
  return {
    type: 'Buffer',
    data: Array.prototype.slice.call(this._arr || this, 0)
  }
}

function base64Slice (buf, start, end) {
  if (start === 0 && end === buf.length) {
    return base64.fromByteArray(buf)
  } else {
    return base64.fromByteArray(buf.slice(start, end))
  }
}

function utf8Slice (buf, start, end) {
  end = Math.min(buf.length, end)
  var res = []

  var i = start
  while (i < end) {
    var firstByte = buf[i]
    var codePoint = null
    var bytesPerSequence = (firstByte > 0xEF) ? 4
      : (firstByte > 0xDF) ? 3
      : (firstByte > 0xBF) ? 2
      : 1

    if (i + bytesPerSequence <= end) {
      var secondByte, thirdByte, fourthByte, tempCodePoint

      switch (bytesPerSequence) {
        case 1:
          if (firstByte < 0x80) {
            codePoint = firstByte
          }
          break
        case 2:
          secondByte = buf[i + 1]
          if ((secondByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0x1F) << 0x6 | (secondByte & 0x3F)
            if (tempCodePoint > 0x7F) {
              codePoint = tempCodePoint
            }
          }
          break
        case 3:
          secondByte = buf[i + 1]
          thirdByte = buf[i + 2]
          if ((secondByte & 0xC0) === 0x80 && (thirdByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0xF) << 0xC | (secondByte & 0x3F) << 0x6 | (thirdByte & 0x3F)
            if (tempCodePoint > 0x7FF && (tempCodePoint < 0xD800 || tempCodePoint > 0xDFFF)) {
              codePoint = tempCodePoint
            }
          }
          break
        case 4:
          secondByte = buf[i + 1]
          thirdByte = buf[i + 2]
          fourthByte = buf[i + 3]
          if ((secondByte & 0xC0) === 0x80 && (thirdByte & 0xC0) === 0x80 && (fourthByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0xF) << 0x12 | (secondByte & 0x3F) << 0xC | (thirdByte & 0x3F) << 0x6 | (fourthByte & 0x3F)
            if (tempCodePoint > 0xFFFF && tempCodePoint < 0x110000) {
              codePoint = tempCodePoint
            }
          }
      }
    }

    if (codePoint === null) {
      // we did not generate a valid codePoint so insert a
      // replacement char (U+FFFD) and advance only 1 byte
      codePoint = 0xFFFD
      bytesPerSequence = 1
    } else if (codePoint > 0xFFFF) {
      // encode to utf16 (surrogate pair dance)
      codePoint -= 0x10000
      res.push(codePoint >>> 10 & 0x3FF | 0xD800)
      codePoint = 0xDC00 | codePoint & 0x3FF
    }

    res.push(codePoint)
    i += bytesPerSequence
  }

  return decodeCodePointsArray(res)
}

// Based on http://stackoverflow.com/a/22747272/680742, the browser with
// the lowest limit is Chrome, with 0x10000 args.
// We go 1 magnitude less, for safety
var MAX_ARGUMENTS_LENGTH = 0x1000

function decodeCodePointsArray (codePoints) {
  var len = codePoints.length
  if (len <= MAX_ARGUMENTS_LENGTH) {
    return String.fromCharCode.apply(String, codePoints) // avoid extra slice()
  }

  // Decode in chunks to avoid "call stack size exceeded".
  var res = ''
  var i = 0
  while (i < len) {
    res += String.fromCharCode.apply(
      String,
      codePoints.slice(i, i += MAX_ARGUMENTS_LENGTH)
    )
  }
  return res
}

function asciiSlice (buf, start, end) {
  var ret = ''
  end = Math.min(buf.length, end)

  for (var i = start; i < end; ++i) {
    ret += String.fromCharCode(buf[i] & 0x7F)
  }
  return ret
}

function latin1Slice (buf, start, end) {
  var ret = ''
  end = Math.min(buf.length, end)

  for (var i = start; i < end; ++i) {
    ret += String.fromCharCode(buf[i])
  }
  return ret
}

function hexSlice (buf, start, end) {
  var len = buf.length

  if (!start || start < 0) start = 0
  if (!end || end < 0 || end > len) end = len

  var out = ''
  for (var i = start; i < end; ++i) {
    out += toHex(buf[i])
  }
  return out
}

function utf16leSlice (buf, start, end) {
  var bytes = buf.slice(start, end)
  var res = ''
  for (var i = 0; i < bytes.length; i += 2) {
    res += String.fromCharCode(bytes[i] + bytes[i + 1] * 256)
  }
  return res
}

Buffer.prototype.slice = function slice (start, end) {
  var len = this.length
  start = ~~start
  end = end === undefined ? len : ~~end

  if (start < 0) {
    start += len
    if (start < 0) start = 0
  } else if (start > len) {
    start = len
  }

  if (end < 0) {
    end += len
    if (end < 0) end = 0
  } else if (end > len) {
    end = len
  }

  if (end < start) end = start

  var newBuf
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    newBuf = this.subarray(start, end)
    newBuf.__proto__ = Buffer.prototype
  } else {
    var sliceLen = end - start
    newBuf = new Buffer(sliceLen, undefined)
    for (var i = 0; i < sliceLen; ++i) {
      newBuf[i] = this[i + start]
    }
  }

  return newBuf
}

/*
 * Need to make sure that buffer isn't trying to write out of bounds.
 */
function checkOffset (offset, ext, length) {
  if ((offset % 1) !== 0 || offset < 0) throw new RangeError('offset is not uint')
  if (offset + ext > length) throw new RangeError('Trying to access beyond buffer length')
}

Buffer.prototype.readUIntLE = function readUIntLE (offset, byteLength, noAssert) {
  offset = offset | 0
  byteLength = byteLength | 0
  if (!noAssert) checkOffset(offset, byteLength, this.length)

  var val = this[offset]
  var mul = 1
  var i = 0
  while (++i < byteLength && (mul *= 0x100)) {
    val += this[offset + i] * mul
  }

  return val
}

Buffer.prototype.readUIntBE = function readUIntBE (offset, byteLength, noAssert) {
  offset = offset | 0
  byteLength = byteLength | 0
  if (!noAssert) {
    checkOffset(offset, byteLength, this.length)
  }

  var val = this[offset + --byteLength]
  var mul = 1
  while (byteLength > 0 && (mul *= 0x100)) {
    val += this[offset + --byteLength] * mul
  }

  return val
}

Buffer.prototype.readUInt8 = function readUInt8 (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 1, this.length)
  return this[offset]
}

Buffer.prototype.readUInt16LE = function readUInt16LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length)
  return this[offset] | (this[offset + 1] << 8)
}

Buffer.prototype.readUInt16BE = function readUInt16BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length)
  return (this[offset] << 8) | this[offset + 1]
}

Buffer.prototype.readUInt32LE = function readUInt32LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length)

  return ((this[offset]) |
      (this[offset + 1] << 8) |
      (this[offset + 2] << 16)) +
      (this[offset + 3] * 0x1000000)
}

Buffer.prototype.readUInt32BE = function readUInt32BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length)

  return (this[offset] * 0x1000000) +
    ((this[offset + 1] << 16) |
    (this[offset + 2] << 8) |
    this[offset + 3])
}

Buffer.prototype.readIntLE = function readIntLE (offset, byteLength, noAssert) {
  offset = offset | 0
  byteLength = byteLength | 0
  if (!noAssert) checkOffset(offset, byteLength, this.length)

  var val = this[offset]
  var mul = 1
  var i = 0
  while (++i < byteLength && (mul *= 0x100)) {
    val += this[offset + i] * mul
  }
  mul *= 0x80

  if (val >= mul) val -= Math.pow(2, 8 * byteLength)

  return val
}

Buffer.prototype.readIntBE = function readIntBE (offset, byteLength, noAssert) {
  offset = offset | 0
  byteLength = byteLength | 0
  if (!noAssert) checkOffset(offset, byteLength, this.length)

  var i = byteLength
  var mul = 1
  var val = this[offset + --i]
  while (i > 0 && (mul *= 0x100)) {
    val += this[offset + --i] * mul
  }
  mul *= 0x80

  if (val >= mul) val -= Math.pow(2, 8 * byteLength)

  return val
}

Buffer.prototype.readInt8 = function readInt8 (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 1, this.length)
  if (!(this[offset] & 0x80)) return (this[offset])
  return ((0xff - this[offset] + 1) * -1)
}

Buffer.prototype.readInt16LE = function readInt16LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length)
  var val = this[offset] | (this[offset + 1] << 8)
  return (val & 0x8000) ? val | 0xFFFF0000 : val
}

Buffer.prototype.readInt16BE = function readInt16BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length)
  var val = this[offset + 1] | (this[offset] << 8)
  return (val & 0x8000) ? val | 0xFFFF0000 : val
}

Buffer.prototype.readInt32LE = function readInt32LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length)

  return (this[offset]) |
    (this[offset + 1] << 8) |
    (this[offset + 2] << 16) |
    (this[offset + 3] << 24)
}

Buffer.prototype.readInt32BE = function readInt32BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length)

  return (this[offset] << 24) |
    (this[offset + 1] << 16) |
    (this[offset + 2] << 8) |
    (this[offset + 3])
}

Buffer.prototype.readFloatLE = function readFloatLE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length)
  return ieee754.read(this, offset, true, 23, 4)
}

Buffer.prototype.readFloatBE = function readFloatBE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length)
  return ieee754.read(this, offset, false, 23, 4)
}

Buffer.prototype.readDoubleLE = function readDoubleLE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 8, this.length)
  return ieee754.read(this, offset, true, 52, 8)
}

Buffer.prototype.readDoubleBE = function readDoubleBE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 8, this.length)
  return ieee754.read(this, offset, false, 52, 8)
}

function checkInt (buf, value, offset, ext, max, min) {
  if (!Buffer.isBuffer(buf)) throw new TypeError('"buffer" argument must be a Buffer instance')
  if (value > max || value < min) throw new RangeError('"value" argument is out of bounds')
  if (offset + ext > buf.length) throw new RangeError('Index out of range')
}

Buffer.prototype.writeUIntLE = function writeUIntLE (value, offset, byteLength, noAssert) {
  value = +value
  offset = offset | 0
  byteLength = byteLength | 0
  if (!noAssert) {
    var maxBytes = Math.pow(2, 8 * byteLength) - 1
    checkInt(this, value, offset, byteLength, maxBytes, 0)
  }

  var mul = 1
  var i = 0
  this[offset] = value & 0xFF
  while (++i < byteLength && (mul *= 0x100)) {
    this[offset + i] = (value / mul) & 0xFF
  }

  return offset + byteLength
}

Buffer.prototype.writeUIntBE = function writeUIntBE (value, offset, byteLength, noAssert) {
  value = +value
  offset = offset | 0
  byteLength = byteLength | 0
  if (!noAssert) {
    var maxBytes = Math.pow(2, 8 * byteLength) - 1
    checkInt(this, value, offset, byteLength, maxBytes, 0)
  }

  var i = byteLength - 1
  var mul = 1
  this[offset + i] = value & 0xFF
  while (--i >= 0 && (mul *= 0x100)) {
    this[offset + i] = (value / mul) & 0xFF
  }

  return offset + byteLength
}

Buffer.prototype.writeUInt8 = function writeUInt8 (value, offset, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) checkInt(this, value, offset, 1, 0xff, 0)
  if (!Buffer.TYPED_ARRAY_SUPPORT) value = Math.floor(value)
  this[offset] = (value & 0xff)
  return offset + 1
}

function objectWriteUInt16 (buf, value, offset, littleEndian) {
  if (value < 0) value = 0xffff + value + 1
  for (var i = 0, j = Math.min(buf.length - offset, 2); i < j; ++i) {
    buf[offset + i] = (value & (0xff << (8 * (littleEndian ? i : 1 - i)))) >>>
      (littleEndian ? i : 1 - i) * 8
  }
}

Buffer.prototype.writeUInt16LE = function writeUInt16LE (value, offset, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) checkInt(this, value, offset, 2, 0xffff, 0)
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value & 0xff)
    this[offset + 1] = (value >>> 8)
  } else {
    objectWriteUInt16(this, value, offset, true)
  }
  return offset + 2
}

Buffer.prototype.writeUInt16BE = function writeUInt16BE (value, offset, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) checkInt(this, value, offset, 2, 0xffff, 0)
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 8)
    this[offset + 1] = (value & 0xff)
  } else {
    objectWriteUInt16(this, value, offset, false)
  }
  return offset + 2
}

function objectWriteUInt32 (buf, value, offset, littleEndian) {
  if (value < 0) value = 0xffffffff + value + 1
  for (var i = 0, j = Math.min(buf.length - offset, 4); i < j; ++i) {
    buf[offset + i] = (value >>> (littleEndian ? i : 3 - i) * 8) & 0xff
  }
}

Buffer.prototype.writeUInt32LE = function writeUInt32LE (value, offset, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) checkInt(this, value, offset, 4, 0xffffffff, 0)
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset + 3] = (value >>> 24)
    this[offset + 2] = (value >>> 16)
    this[offset + 1] = (value >>> 8)
    this[offset] = (value & 0xff)
  } else {
    objectWriteUInt32(this, value, offset, true)
  }
  return offset + 4
}

Buffer.prototype.writeUInt32BE = function writeUInt32BE (value, offset, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) checkInt(this, value, offset, 4, 0xffffffff, 0)
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 24)
    this[offset + 1] = (value >>> 16)
    this[offset + 2] = (value >>> 8)
    this[offset + 3] = (value & 0xff)
  } else {
    objectWriteUInt32(this, value, offset, false)
  }
  return offset + 4
}

Buffer.prototype.writeIntLE = function writeIntLE (value, offset, byteLength, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) {
    var limit = Math.pow(2, 8 * byteLength - 1)

    checkInt(this, value, offset, byteLength, limit - 1, -limit)
  }

  var i = 0
  var mul = 1
  var sub = 0
  this[offset] = value & 0xFF
  while (++i < byteLength && (mul *= 0x100)) {
    if (value < 0 && sub === 0 && this[offset + i - 1] !== 0) {
      sub = 1
    }
    this[offset + i] = ((value / mul) >> 0) - sub & 0xFF
  }

  return offset + byteLength
}

Buffer.prototype.writeIntBE = function writeIntBE (value, offset, byteLength, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) {
    var limit = Math.pow(2, 8 * byteLength - 1)

    checkInt(this, value, offset, byteLength, limit - 1, -limit)
  }

  var i = byteLength - 1
  var mul = 1
  var sub = 0
  this[offset + i] = value & 0xFF
  while (--i >= 0 && (mul *= 0x100)) {
    if (value < 0 && sub === 0 && this[offset + i + 1] !== 0) {
      sub = 1
    }
    this[offset + i] = ((value / mul) >> 0) - sub & 0xFF
  }

  return offset + byteLength
}

Buffer.prototype.writeInt8 = function writeInt8 (value, offset, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) checkInt(this, value, offset, 1, 0x7f, -0x80)
  if (!Buffer.TYPED_ARRAY_SUPPORT) value = Math.floor(value)
  if (value < 0) value = 0xff + value + 1
  this[offset] = (value & 0xff)
  return offset + 1
}

Buffer.prototype.writeInt16LE = function writeInt16LE (value, offset, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) checkInt(this, value, offset, 2, 0x7fff, -0x8000)
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value & 0xff)
    this[offset + 1] = (value >>> 8)
  } else {
    objectWriteUInt16(this, value, offset, true)
  }
  return offset + 2
}

Buffer.prototype.writeInt16BE = function writeInt16BE (value, offset, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) checkInt(this, value, offset, 2, 0x7fff, -0x8000)
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 8)
    this[offset + 1] = (value & 0xff)
  } else {
    objectWriteUInt16(this, value, offset, false)
  }
  return offset + 2
}

Buffer.prototype.writeInt32LE = function writeInt32LE (value, offset, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) checkInt(this, value, offset, 4, 0x7fffffff, -0x80000000)
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value & 0xff)
    this[offset + 1] = (value >>> 8)
    this[offset + 2] = (value >>> 16)
    this[offset + 3] = (value >>> 24)
  } else {
    objectWriteUInt32(this, value, offset, true)
  }
  return offset + 4
}

Buffer.prototype.writeInt32BE = function writeInt32BE (value, offset, noAssert) {
  value = +value
  offset = offset | 0
  if (!noAssert) checkInt(this, value, offset, 4, 0x7fffffff, -0x80000000)
  if (value < 0) value = 0xffffffff + value + 1
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 24)
    this[offset + 1] = (value >>> 16)
    this[offset + 2] = (value >>> 8)
    this[offset + 3] = (value & 0xff)
  } else {
    objectWriteUInt32(this, value, offset, false)
  }
  return offset + 4
}

function checkIEEE754 (buf, value, offset, ext, max, min) {
  if (offset + ext > buf.length) throw new RangeError('Index out of range')
  if (offset < 0) throw new RangeError('Index out of range')
}

function writeFloat (buf, value, offset, littleEndian, noAssert) {
  if (!noAssert) {
    checkIEEE754(buf, value, offset, 4, 3.4028234663852886e+38, -3.4028234663852886e+38)
  }
  ieee754.write(buf, value, offset, littleEndian, 23, 4)
  return offset + 4
}

Buffer.prototype.writeFloatLE = function writeFloatLE (value, offset, noAssert) {
  return writeFloat(this, value, offset, true, noAssert)
}

Buffer.prototype.writeFloatBE = function writeFloatBE (value, offset, noAssert) {
  return writeFloat(this, value, offset, false, noAssert)
}

function writeDouble (buf, value, offset, littleEndian, noAssert) {
  if (!noAssert) {
    checkIEEE754(buf, value, offset, 8, 1.7976931348623157E+308, -1.7976931348623157E+308)
  }
  ieee754.write(buf, value, offset, littleEndian, 52, 8)
  return offset + 8
}

Buffer.prototype.writeDoubleLE = function writeDoubleLE (value, offset, noAssert) {
  return writeDouble(this, value, offset, true, noAssert)
}

Buffer.prototype.writeDoubleBE = function writeDoubleBE (value, offset, noAssert) {
  return writeDouble(this, value, offset, false, noAssert)
}

// copy(targetBuffer, targetStart=0, sourceStart=0, sourceEnd=buffer.length)
Buffer.prototype.copy = function copy (target, targetStart, start, end) {
  if (!start) start = 0
  if (!end && end !== 0) end = this.length
  if (targetStart >= target.length) targetStart = target.length
  if (!targetStart) targetStart = 0
  if (end > 0 && end < start) end = start

  // Copy 0 bytes; we're done
  if (end === start) return 0
  if (target.length === 0 || this.length === 0) return 0

  // Fatal error conditions
  if (targetStart < 0) {
    throw new RangeError('targetStart out of bounds')
  }
  if (start < 0 || start >= this.length) throw new RangeError('sourceStart out of bounds')
  if (end < 0) throw new RangeError('sourceEnd out of bounds')

  // Are we oob?
  if (end > this.length) end = this.length
  if (target.length - targetStart < end - start) {
    end = target.length - targetStart + start
  }

  var len = end - start
  var i

  if (this === target && start < targetStart && targetStart < end) {
    // descending copy from end
    for (i = len - 1; i >= 0; --i) {
      target[i + targetStart] = this[i + start]
    }
  } else if (len < 1000 || !Buffer.TYPED_ARRAY_SUPPORT) {
    // ascending copy from start
    for (i = 0; i < len; ++i) {
      target[i + targetStart] = this[i + start]
    }
  } else {
    Uint8Array.prototype.set.call(
      target,
      this.subarray(start, start + len),
      targetStart
    )
  }

  return len
}

// Usage:
//    buffer.fill(number[, offset[, end]])
//    buffer.fill(buffer[, offset[, end]])
//    buffer.fill(string[, offset[, end]][, encoding])
Buffer.prototype.fill = function fill (val, start, end, encoding) {
  // Handle string cases:
  if (typeof val === 'string') {
    if (typeof start === 'string') {
      encoding = start
      start = 0
      end = this.length
    } else if (typeof end === 'string') {
      encoding = end
      end = this.length
    }
    if (val.length === 1) {
      var code = val.charCodeAt(0)
      if (code < 256) {
        val = code
      }
    }
    if (encoding !== undefined && typeof encoding !== 'string') {
      throw new TypeError('encoding must be a string')
    }
    if (typeof encoding === 'string' && !Buffer.isEncoding(encoding)) {
      throw new TypeError('Unknown encoding: ' + encoding)
    }
  } else if (typeof val === 'number') {
    val = val & 255
  }

  // Invalid ranges are not set to a default, so can range check early.
  if (start < 0 || this.length < start || this.length < end) {
    throw new RangeError('Out of range index')
  }

  if (end <= start) {
    return this
  }

  start = start >>> 0
  end = end === undefined ? this.length : end >>> 0

  if (!val) val = 0

  var i
  if (typeof val === 'number') {
    for (i = start; i < end; ++i) {
      this[i] = val
    }
  } else {
    var bytes = Buffer.isBuffer(val)
      ? val
      : utf8ToBytes(new Buffer(val, encoding).toString())
    var len = bytes.length
    for (i = 0; i < end - start; ++i) {
      this[i + start] = bytes[i % len]
    }
  }

  return this
}

// HELPER FUNCTIONS
// ================

var INVALID_BASE64_RE = /[^+\/0-9A-Za-z-_]/g

function base64clean (str) {
  // Node strips out invalid characters like \n and \t from the string, base64-js does not
  str = stringtrim(str).replace(INVALID_BASE64_RE, '')
  // Node converts strings with length < 2 to ''
  if (str.length < 2) return ''
  // Node allows for non-padded base64 strings (missing trailing ===), base64-js does not
  while (str.length % 4 !== 0) {
    str = str + '='
  }
  return str
}

function stringtrim (str) {
  if (str.trim) return str.trim()
  return str.replace(/^\s+|\s+$/g, '')
}

function toHex (n) {
  if (n < 16) return '0' + n.toString(16)
  return n.toString(16)
}

function utf8ToBytes (string, units) {
  units = units || Infinity
  var codePoint
  var length = string.length
  var leadSurrogate = null
  var bytes = []

  for (var i = 0; i < length; ++i) {
    codePoint = string.charCodeAt(i)

    // is surrogate component
    if (codePoint > 0xD7FF && codePoint < 0xE000) {
      // last char was a lead
      if (!leadSurrogate) {
        // no lead yet
        if (codePoint > 0xDBFF) {
          // unexpected trail
          if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD)
          continue
        } else if (i + 1 === length) {
          // unpaired lead
          if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD)
          continue
        }

        // valid lead
        leadSurrogate = codePoint

        continue
      }

      // 2 leads in a row
      if (codePoint < 0xDC00) {
        if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD)
        leadSurrogate = codePoint
        continue
      }

      // valid surrogate pair
      codePoint = (leadSurrogate - 0xD800 << 10 | codePoint - 0xDC00) + 0x10000
    } else if (leadSurrogate) {
      // valid bmp char, but last char was a lead
      if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD)
    }

    leadSurrogate = null

    // encode utf8
    if (codePoint < 0x80) {
      if ((units -= 1) < 0) break
      bytes.push(codePoint)
    } else if (codePoint < 0x800) {
      if ((units -= 2) < 0) break
      bytes.push(
        codePoint >> 0x6 | 0xC0,
        codePoint & 0x3F | 0x80
      )
    } else if (codePoint < 0x10000) {
      if ((units -= 3) < 0) break
      bytes.push(
        codePoint >> 0xC | 0xE0,
        codePoint >> 0x6 & 0x3F | 0x80,
        codePoint & 0x3F | 0x80
      )
    } else if (codePoint < 0x110000) {
      if ((units -= 4) < 0) break
      bytes.push(
        codePoint >> 0x12 | 0xF0,
        codePoint >> 0xC & 0x3F | 0x80,
        codePoint >> 0x6 & 0x3F | 0x80,
        codePoint & 0x3F | 0x80
      )
    } else {
      throw new Error('Invalid code point')
    }
  }

  return bytes
}

function asciiToBytes (str) {
  var byteArray = []
  for (var i = 0; i < str.length; ++i) {
    // Node's code seems to be doing this and not & 0x7F..
    byteArray.push(str.charCodeAt(i) & 0xFF)
  }
  return byteArray
}

function utf16leToBytes (str, units) {
  var c, hi, lo
  var byteArray = []
  for (var i = 0; i < str.length; ++i) {
    if ((units -= 2) < 0) break

    c = str.charCodeAt(i)
    hi = c >> 8
    lo = c % 256
    byteArray.push(lo)
    byteArray.push(hi)
  }

  return byteArray
}

function base64ToBytes (str) {
  return base64.toByteArray(base64clean(str))
}

function blitBuffer (src, dst, offset, length) {
  for (var i = 0; i < length; ++i) {
    if ((i + offset >= dst.length) || (i >= src.length)) break
    dst[i + offset] = src[i]
  }
  return i
}

function isnan (val) {
  return val !== val // eslint-disable-line no-self-compare
}

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(24)))

/***/ }),
/* 11 */
/***/ (function(module, exports, __webpack_require__) {

var fails = __webpack_require__(4);

// Thank's IE8 for his funny defineProperty
module.exports = !fails(function () {
  return Object.defineProperty({}, 1, { get: function () { return 7; } })[1] != 7;
});


/***/ }),
/* 12 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(49));
	}
	else {}
}(this, function (CryptoJS) {

	/**
	 * Cipher core components.
	 */
	CryptoJS.lib.Cipher || (function (undefined) {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var Base = C_lib.Base;
	    var WordArray = C_lib.WordArray;
	    var BufferedBlockAlgorithm = C_lib.BufferedBlockAlgorithm;
	    var C_enc = C.enc;
	    var Utf8 = C_enc.Utf8;
	    var Base64 = C_enc.Base64;
	    var C_algo = C.algo;
	    var EvpKDF = C_algo.EvpKDF;

	    /**
	     * Abstract base cipher template.
	     *
	     * @property {number} keySize This cipher's key size. Default: 4 (128 bits)
	     * @property {number} ivSize This cipher's IV size. Default: 4 (128 bits)
	     * @property {number} _ENC_XFORM_MODE A constant representing encryption mode.
	     * @property {number} _DEC_XFORM_MODE A constant representing decryption mode.
	     */
	    var Cipher = C_lib.Cipher = BufferedBlockAlgorithm.extend({
	        /**
	         * Configuration options.
	         *
	         * @property {WordArray} iv The IV to use for this operation.
	         */
	        cfg: Base.extend(),

	        /**
	         * Creates this cipher in encryption mode.
	         *
	         * @param {WordArray} key The key.
	         * @param {Object} cfg (Optional) The configuration options to use for this operation.
	         *
	         * @return {Cipher} A cipher instance.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var cipher = CryptoJS.algo.AES.createEncryptor(keyWordArray, { iv: ivWordArray });
	         */
	        createEncryptor: function (key, cfg) {
	            return this.create(this._ENC_XFORM_MODE, key, cfg);
	        },

	        /**
	         * Creates this cipher in decryption mode.
	         *
	         * @param {WordArray} key The key.
	         * @param {Object} cfg (Optional) The configuration options to use for this operation.
	         *
	         * @return {Cipher} A cipher instance.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var cipher = CryptoJS.algo.AES.createDecryptor(keyWordArray, { iv: ivWordArray });
	         */
	        createDecryptor: function (key, cfg) {
	            return this.create(this._DEC_XFORM_MODE, key, cfg);
	        },

	        /**
	         * Initializes a newly created cipher.
	         *
	         * @param {number} xformMode Either the encryption or decryption transormation mode constant.
	         * @param {WordArray} key The key.
	         * @param {Object} cfg (Optional) The configuration options to use for this operation.
	         *
	         * @example
	         *
	         *     var cipher = CryptoJS.algo.AES.create(CryptoJS.algo.AES._ENC_XFORM_MODE, keyWordArray, { iv: ivWordArray });
	         */
	        init: function (xformMode, key, cfg) {
	            // Apply config defaults
	            this.cfg = this.cfg.extend(cfg);

	            // Store transform mode and key
	            this._xformMode = xformMode;
	            this._key = key;

	            // Set initial values
	            this.reset();
	        },

	        /**
	         * Resets this cipher to its initial state.
	         *
	         * @example
	         *
	         *     cipher.reset();
	         */
	        reset: function () {
	            // Reset data buffer
	            BufferedBlockAlgorithm.reset.call(this);

	            // Perform concrete-cipher logic
	            this._doReset();
	        },

	        /**
	         * Adds data to be encrypted or decrypted.
	         *
	         * @param {WordArray|string} dataUpdate The data to encrypt or decrypt.
	         *
	         * @return {WordArray} The data after processing.
	         *
	         * @example
	         *
	         *     var encrypted = cipher.process('data');
	         *     var encrypted = cipher.process(wordArray);
	         */
	        process: function (dataUpdate) {
	            // Append
	            this._append(dataUpdate);

	            // Process available blocks
	            return this._process();
	        },

	        /**
	         * Finalizes the encryption or decryption process.
	         * Note that the finalize operation is effectively a destructive, read-once operation.
	         *
	         * @param {WordArray|string} dataUpdate The final data to encrypt or decrypt.
	         *
	         * @return {WordArray} The data after final processing.
	         *
	         * @example
	         *
	         *     var encrypted = cipher.finalize();
	         *     var encrypted = cipher.finalize('data');
	         *     var encrypted = cipher.finalize(wordArray);
	         */
	        finalize: function (dataUpdate) {
	            // Final data update
	            if (dataUpdate) {
	                this._append(dataUpdate);
	            }

	            // Perform concrete-cipher logic
	            var finalProcessedData = this._doFinalize();

	            return finalProcessedData;
	        },

	        keySize: 128/32,

	        ivSize: 128/32,

	        _ENC_XFORM_MODE: 1,

	        _DEC_XFORM_MODE: 2,

	        /**
	         * Creates shortcut functions to a cipher's object interface.
	         *
	         * @param {Cipher} cipher The cipher to create a helper for.
	         *
	         * @return {Object} An object with encrypt and decrypt shortcut functions.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var AES = CryptoJS.lib.Cipher._createHelper(CryptoJS.algo.AES);
	         */
	        _createHelper: (function () {
	            function selectCipherStrategy(key) {
	                if (typeof key == 'string') {
	                    return PasswordBasedCipher;
	                } else {
	                    return SerializableCipher;
	                }
	            }

	            return function (cipher) {
	                return {
	                    encrypt: function (message, key, cfg) {
	                        return selectCipherStrategy(key).encrypt(cipher, message, key, cfg);
	                    },

	                    decrypt: function (ciphertext, key, cfg) {
	                        return selectCipherStrategy(key).decrypt(cipher, ciphertext, key, cfg);
	                    }
	                };
	            };
	        }())
	    });

	    /**
	     * Abstract base stream cipher template.
	     *
	     * @property {number} blockSize The number of 32-bit words this cipher operates on. Default: 1 (32 bits)
	     */
	    var StreamCipher = C_lib.StreamCipher = Cipher.extend({
	        _doFinalize: function () {
	            // Process partial blocks
	            var finalProcessedBlocks = this._process(!!'flush');

	            return finalProcessedBlocks;
	        },

	        blockSize: 1
	    });

	    /**
	     * Mode namespace.
	     */
	    var C_mode = C.mode = {};

	    /**
	     * Abstract base block cipher mode template.
	     */
	    var BlockCipherMode = C_lib.BlockCipherMode = Base.extend({
	        /**
	         * Creates this mode for encryption.
	         *
	         * @param {Cipher} cipher A block cipher instance.
	         * @param {Array} iv The IV words.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var mode = CryptoJS.mode.CBC.createEncryptor(cipher, iv.words);
	         */
	        createEncryptor: function (cipher, iv) {
	            return this.Encryptor.create(cipher, iv);
	        },

	        /**
	         * Creates this mode for decryption.
	         *
	         * @param {Cipher} cipher A block cipher instance.
	         * @param {Array} iv The IV words.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var mode = CryptoJS.mode.CBC.createDecryptor(cipher, iv.words);
	         */
	        createDecryptor: function (cipher, iv) {
	            return this.Decryptor.create(cipher, iv);
	        },

	        /**
	         * Initializes a newly created mode.
	         *
	         * @param {Cipher} cipher A block cipher instance.
	         * @param {Array} iv The IV words.
	         *
	         * @example
	         *
	         *     var mode = CryptoJS.mode.CBC.Encryptor.create(cipher, iv.words);
	         */
	        init: function (cipher, iv) {
	            this._cipher = cipher;
	            this._iv = iv;
	        }
	    });

	    /**
	     * Cipher Block Chaining mode.
	     */
	    var CBC = C_mode.CBC = (function () {
	        /**
	         * Abstract base CBC mode.
	         */
	        var CBC = BlockCipherMode.extend();

	        /**
	         * CBC encryptor.
	         */
	        CBC.Encryptor = CBC.extend({
	            /**
	             * Processes the data block at offset.
	             *
	             * @param {Array} words The data words to operate on.
	             * @param {number} offset The offset where the block starts.
	             *
	             * @example
	             *
	             *     mode.processBlock(data.words, offset);
	             */
	            processBlock: function (words, offset) {
	                // Shortcuts
	                var cipher = this._cipher;
	                var blockSize = cipher.blockSize;

	                // XOR and encrypt
	                xorBlock.call(this, words, offset, blockSize);
	                cipher.encryptBlock(words, offset);

	                // Remember this block to use with next block
	                this._prevBlock = words.slice(offset, offset + blockSize);
	            }
	        });

	        /**
	         * CBC decryptor.
	         */
	        CBC.Decryptor = CBC.extend({
	            /**
	             * Processes the data block at offset.
	             *
	             * @param {Array} words The data words to operate on.
	             * @param {number} offset The offset where the block starts.
	             *
	             * @example
	             *
	             *     mode.processBlock(data.words, offset);
	             */
	            processBlock: function (words, offset) {
	                // Shortcuts
	                var cipher = this._cipher;
	                var blockSize = cipher.blockSize;

	                // Remember this block to use with next block
	                var thisBlock = words.slice(offset, offset + blockSize);

	                // Decrypt and XOR
	                cipher.decryptBlock(words, offset);
	                xorBlock.call(this, words, offset, blockSize);

	                // This block becomes the previous block
	                this._prevBlock = thisBlock;
	            }
	        });

	        function xorBlock(words, offset, blockSize) {
	            // Shortcut
	            var iv = this._iv;

	            // Choose mixing block
	            if (iv) {
	                var block = iv;

	                // Remove IV for subsequent blocks
	                this._iv = undefined;
	            } else {
	                var block = this._prevBlock;
	            }

	            // XOR blocks
	            for (var i = 0; i < blockSize; i++) {
	                words[offset + i] ^= block[i];
	            }
	        }

	        return CBC;
	    }());

	    /**
	     * Padding namespace.
	     */
	    var C_pad = C.pad = {};

	    /**
	     * PKCS #5/7 padding strategy.
	     */
	    var Pkcs7 = C_pad.Pkcs7 = {
	        /**
	         * Pads data using the algorithm defined in PKCS #5/7.
	         *
	         * @param {WordArray} data The data to pad.
	         * @param {number} blockSize The multiple that the data should be padded to.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     CryptoJS.pad.Pkcs7.pad(wordArray, 4);
	         */
	        pad: function (data, blockSize) {
	            // Shortcut
	            var blockSizeBytes = blockSize * 4;

	            // Count padding bytes
	            var nPaddingBytes = blockSizeBytes - data.sigBytes % blockSizeBytes;

	            // Create padding word
	            var paddingWord = (nPaddingBytes << 24) | (nPaddingBytes << 16) | (nPaddingBytes << 8) | nPaddingBytes;

	            // Create padding
	            var paddingWords = [];
	            for (var i = 0; i < nPaddingBytes; i += 4) {
	                paddingWords.push(paddingWord);
	            }
	            var padding = WordArray.create(paddingWords, nPaddingBytes);

	            // Add padding
	            data.concat(padding);
	        },

	        /**
	         * Unpads data that had been padded using the algorithm defined in PKCS #5/7.
	         *
	         * @param {WordArray} data The data to unpad.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     CryptoJS.pad.Pkcs7.unpad(wordArray);
	         */
	        unpad: function (data) {
	            // Get number of padding bytes from last byte
	            var nPaddingBytes = data.words[(data.sigBytes - 1) >>> 2] & 0xff;

	            // Remove padding
	            data.sigBytes -= nPaddingBytes;
	        }
	    };

	    /**
	     * Abstract base block cipher template.
	     *
	     * @property {number} blockSize The number of 32-bit words this cipher operates on. Default: 4 (128 bits)
	     */
	    var BlockCipher = C_lib.BlockCipher = Cipher.extend({
	        /**
	         * Configuration options.
	         *
	         * @property {Mode} mode The block mode to use. Default: CBC
	         * @property {Padding} padding The padding strategy to use. Default: Pkcs7
	         */
	        cfg: Cipher.cfg.extend({
	            mode: CBC,
	            padding: Pkcs7
	        }),

	        reset: function () {
	            // Reset cipher
	            Cipher.reset.call(this);

	            // Shortcuts
	            var cfg = this.cfg;
	            var iv = cfg.iv;
	            var mode = cfg.mode;

	            // Reset block mode
	            if (this._xformMode == this._ENC_XFORM_MODE) {
	                var modeCreator = mode.createEncryptor;
	            } else /* if (this._xformMode == this._DEC_XFORM_MODE) */ {
	                var modeCreator = mode.createDecryptor;
	                // Keep at least one block in the buffer for unpadding
	                this._minBufferSize = 1;
	            }

	            if (this._mode && this._mode.__creator == modeCreator) {
	                this._mode.init(this, iv && iv.words);
	            } else {
	                this._mode = modeCreator.call(mode, this, iv && iv.words);
	                this._mode.__creator = modeCreator;
	            }
	        },

	        _doProcessBlock: function (words, offset) {
	            this._mode.processBlock(words, offset);
	        },

	        _doFinalize: function () {
	            // Shortcut
	            var padding = this.cfg.padding;

	            // Finalize
	            if (this._xformMode == this._ENC_XFORM_MODE) {
	                // Pad data
	                padding.pad(this._data, this.blockSize);

	                // Process final blocks
	                var finalProcessedBlocks = this._process(!!'flush');
	            } else /* if (this._xformMode == this._DEC_XFORM_MODE) */ {
	                // Process final blocks
	                var finalProcessedBlocks = this._process(!!'flush');

	                // Unpad data
	                padding.unpad(finalProcessedBlocks);
	            }

	            return finalProcessedBlocks;
	        },

	        blockSize: 128/32
	    });

	    /**
	     * A collection of cipher parameters.
	     *
	     * @property {WordArray} ciphertext The raw ciphertext.
	     * @property {WordArray} key The key to this ciphertext.
	     * @property {WordArray} iv The IV used in the ciphering operation.
	     * @property {WordArray} salt The salt used with a key derivation function.
	     * @property {Cipher} algorithm The cipher algorithm.
	     * @property {Mode} mode The block mode used in the ciphering operation.
	     * @property {Padding} padding The padding scheme used in the ciphering operation.
	     * @property {number} blockSize The block size of the cipher.
	     * @property {Format} formatter The default formatting strategy to convert this cipher params object to a string.
	     */
	    var CipherParams = C_lib.CipherParams = Base.extend({
	        /**
	         * Initializes a newly created cipher params object.
	         *
	         * @param {Object} cipherParams An object with any of the possible cipher parameters.
	         *
	         * @example
	         *
	         *     var cipherParams = CryptoJS.lib.CipherParams.create({
	         *         ciphertext: ciphertextWordArray,
	         *         key: keyWordArray,
	         *         iv: ivWordArray,
	         *         salt: saltWordArray,
	         *         algorithm: CryptoJS.algo.AES,
	         *         mode: CryptoJS.mode.CBC,
	         *         padding: CryptoJS.pad.PKCS7,
	         *         blockSize: 4,
	         *         formatter: CryptoJS.format.OpenSSL
	         *     });
	         */
	        init: function (cipherParams) {
	            this.mixIn(cipherParams);
	        },

	        /**
	         * Converts this cipher params object to a string.
	         *
	         * @param {Format} formatter (Optional) The formatting strategy to use.
	         *
	         * @return {string} The stringified cipher params.
	         *
	         * @throws Error If neither the formatter nor the default formatter is set.
	         *
	         * @example
	         *
	         *     var string = cipherParams + '';
	         *     var string = cipherParams.toString();
	         *     var string = cipherParams.toString(CryptoJS.format.OpenSSL);
	         */
	        toString: function (formatter) {
	            return (formatter || this.formatter).stringify(this);
	        }
	    });

	    /**
	     * Format namespace.
	     */
	    var C_format = C.format = {};

	    /**
	     * OpenSSL formatting strategy.
	     */
	    var OpenSSLFormatter = C_format.OpenSSL = {
	        /**
	         * Converts a cipher params object to an OpenSSL-compatible string.
	         *
	         * @param {CipherParams} cipherParams The cipher params object.
	         *
	         * @return {string} The OpenSSL-compatible string.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var openSSLString = CryptoJS.format.OpenSSL.stringify(cipherParams);
	         */
	        stringify: function (cipherParams) {
	            // Shortcuts
	            var ciphertext = cipherParams.ciphertext;
	            var salt = cipherParams.salt;

	            // Format
	            if (salt) {
	                var wordArray = WordArray.create([0x53616c74, 0x65645f5f]).concat(salt).concat(ciphertext);
	            } else {
	                var wordArray = ciphertext;
	            }

	            return wordArray.toString(Base64);
	        },

	        /**
	         * Converts an OpenSSL-compatible string to a cipher params object.
	         *
	         * @param {string} openSSLStr The OpenSSL-compatible string.
	         *
	         * @return {CipherParams} The cipher params object.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var cipherParams = CryptoJS.format.OpenSSL.parse(openSSLString);
	         */
	        parse: function (openSSLStr) {
	            // Parse base64
	            var ciphertext = Base64.parse(openSSLStr);

	            // Shortcut
	            var ciphertextWords = ciphertext.words;

	            // Test for salt
	            if (ciphertextWords[0] == 0x53616c74 && ciphertextWords[1] == 0x65645f5f) {
	                // Extract salt
	                var salt = WordArray.create(ciphertextWords.slice(2, 4));

	                // Remove salt from ciphertext
	                ciphertextWords.splice(0, 4);
	                ciphertext.sigBytes -= 16;
	            }

	            return CipherParams.create({ ciphertext: ciphertext, salt: salt });
	        }
	    };

	    /**
	     * A cipher wrapper that returns ciphertext as a serializable cipher params object.
	     */
	    var SerializableCipher = C_lib.SerializableCipher = Base.extend({
	        /**
	         * Configuration options.
	         *
	         * @property {Formatter} format The formatting strategy to convert cipher param objects to and from a string. Default: OpenSSL
	         */
	        cfg: Base.extend({
	            format: OpenSSLFormatter
	        }),

	        /**
	         * Encrypts a message.
	         *
	         * @param {Cipher} cipher The cipher algorithm to use.
	         * @param {WordArray|string} message The message to encrypt.
	         * @param {WordArray} key The key.
	         * @param {Object} cfg (Optional) The configuration options to use for this operation.
	         *
	         * @return {CipherParams} A cipher params object.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var ciphertextParams = CryptoJS.lib.SerializableCipher.encrypt(CryptoJS.algo.AES, message, key);
	         *     var ciphertextParams = CryptoJS.lib.SerializableCipher.encrypt(CryptoJS.algo.AES, message, key, { iv: iv });
	         *     var ciphertextParams = CryptoJS.lib.SerializableCipher.encrypt(CryptoJS.algo.AES, message, key, { iv: iv, format: CryptoJS.format.OpenSSL });
	         */
	        encrypt: function (cipher, message, key, cfg) {
	            // Apply config defaults
	            cfg = this.cfg.extend(cfg);

	            // Encrypt
	            var encryptor = cipher.createEncryptor(key, cfg);
	            var ciphertext = encryptor.finalize(message);

	            // Shortcut
	            var cipherCfg = encryptor.cfg;

	            // Create and return serializable cipher params
	            return CipherParams.create({
	                ciphertext: ciphertext,
	                key: key,
	                iv: cipherCfg.iv,
	                algorithm: cipher,
	                mode: cipherCfg.mode,
	                padding: cipherCfg.padding,
	                blockSize: cipher.blockSize,
	                formatter: cfg.format
	            });
	        },

	        /**
	         * Decrypts serialized ciphertext.
	         *
	         * @param {Cipher} cipher The cipher algorithm to use.
	         * @param {CipherParams|string} ciphertext The ciphertext to decrypt.
	         * @param {WordArray} key The key.
	         * @param {Object} cfg (Optional) The configuration options to use for this operation.
	         *
	         * @return {WordArray} The plaintext.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var plaintext = CryptoJS.lib.SerializableCipher.decrypt(CryptoJS.algo.AES, formattedCiphertext, key, { iv: iv, format: CryptoJS.format.OpenSSL });
	         *     var plaintext = CryptoJS.lib.SerializableCipher.decrypt(CryptoJS.algo.AES, ciphertextParams, key, { iv: iv, format: CryptoJS.format.OpenSSL });
	         */
	        decrypt: function (cipher, ciphertext, key, cfg) {
	            // Apply config defaults
	            cfg = this.cfg.extend(cfg);

	            // Convert string to CipherParams
	            ciphertext = this._parse(ciphertext, cfg.format);

	            // Decrypt
	            var plaintext = cipher.createDecryptor(key, cfg).finalize(ciphertext.ciphertext);

	            return plaintext;
	        },

	        /**
	         * Converts serialized ciphertext to CipherParams,
	         * else assumed CipherParams already and returns ciphertext unchanged.
	         *
	         * @param {CipherParams|string} ciphertext The ciphertext.
	         * @param {Formatter} format The formatting strategy to use to parse serialized ciphertext.
	         *
	         * @return {CipherParams} The unserialized ciphertext.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var ciphertextParams = CryptoJS.lib.SerializableCipher._parse(ciphertextStringOrParams, format);
	         */
	        _parse: function (ciphertext, format) {
	            if (typeof ciphertext == 'string') {
	                return format.parse(ciphertext, this);
	            } else {
	                return ciphertext;
	            }
	        }
	    });

	    /**
	     * Key derivation function namespace.
	     */
	    var C_kdf = C.kdf = {};

	    /**
	     * OpenSSL key derivation function.
	     */
	    var OpenSSLKdf = C_kdf.OpenSSL = {
	        /**
	         * Derives a key and IV from a password.
	         *
	         * @param {string} password The password to derive from.
	         * @param {number} keySize The size in words of the key to generate.
	         * @param {number} ivSize The size in words of the IV to generate.
	         * @param {WordArray|string} salt (Optional) A 64-bit salt to use. If omitted, a salt will be generated randomly.
	         *
	         * @return {CipherParams} A cipher params object with the key, IV, and salt.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var derivedParams = CryptoJS.kdf.OpenSSL.execute('Password', 256/32, 128/32);
	         *     var derivedParams = CryptoJS.kdf.OpenSSL.execute('Password', 256/32, 128/32, 'saltsalt');
	         */
	        execute: function (password, keySize, ivSize, salt) {
	            // Generate random salt
	            if (!salt) {
	                salt = WordArray.random(64/8);
	            }

	            // Derive key and IV
	            var key = EvpKDF.create({ keySize: keySize + ivSize }).compute(password, salt);

	            // Separate key and IV
	            var iv = WordArray.create(key.words.slice(keySize), ivSize * 4);
	            key.sigBytes = keySize * 4;

	            // Return params
	            return CipherParams.create({ key: key, iv: iv, salt: salt });
	        }
	    };

	    /**
	     * A serializable cipher wrapper that derives the key from a password,
	     * and returns ciphertext as a serializable cipher params object.
	     */
	    var PasswordBasedCipher = C_lib.PasswordBasedCipher = SerializableCipher.extend({
	        /**
	         * Configuration options.
	         *
	         * @property {KDF} kdf The key derivation function to use to generate a key and IV from a password. Default: OpenSSL
	         */
	        cfg: SerializableCipher.cfg.extend({
	            kdf: OpenSSLKdf
	        }),

	        /**
	         * Encrypts a message using a password.
	         *
	         * @param {Cipher} cipher The cipher algorithm to use.
	         * @param {WordArray|string} message The message to encrypt.
	         * @param {string} password The password.
	         * @param {Object} cfg (Optional) The configuration options to use for this operation.
	         *
	         * @return {CipherParams} A cipher params object.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var ciphertextParams = CryptoJS.lib.PasswordBasedCipher.encrypt(CryptoJS.algo.AES, message, 'password');
	         *     var ciphertextParams = CryptoJS.lib.PasswordBasedCipher.encrypt(CryptoJS.algo.AES, message, 'password', { format: CryptoJS.format.OpenSSL });
	         */
	        encrypt: function (cipher, message, password, cfg) {
	            // Apply config defaults
	            cfg = this.cfg.extend(cfg);

	            // Derive key and other params
	            var derivedParams = cfg.kdf.execute(password, cipher.keySize, cipher.ivSize);

	            // Add IV to config
	            cfg.iv = derivedParams.iv;

	            // Encrypt
	            var ciphertext = SerializableCipher.encrypt.call(this, cipher, message, derivedParams.key, cfg);

	            // Mix in derived params
	            ciphertext.mixIn(derivedParams);

	            return ciphertext;
	        },

	        /**
	         * Decrypts serialized ciphertext using a password.
	         *
	         * @param {Cipher} cipher The cipher algorithm to use.
	         * @param {CipherParams|string} ciphertext The ciphertext to decrypt.
	         * @param {string} password The password.
	         * @param {Object} cfg (Optional) The configuration options to use for this operation.
	         *
	         * @return {WordArray} The plaintext.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var plaintext = CryptoJS.lib.PasswordBasedCipher.decrypt(CryptoJS.algo.AES, formattedCiphertext, 'password', { format: CryptoJS.format.OpenSSL });
	         *     var plaintext = CryptoJS.lib.PasswordBasedCipher.decrypt(CryptoJS.algo.AES, ciphertextParams, 'password', { format: CryptoJS.format.OpenSSL });
	         */
	        decrypt: function (cipher, ciphertext, password, cfg) {
	            // Apply config defaults
	            cfg = this.cfg.extend(cfg);

	            // Convert string to CipherParams
	            ciphertext = this._parse(ciphertext, cfg.format);

	            // Derive key and other params
	            var derivedParams = cfg.kdf.execute(password, cipher.keySize, cipher.ivSize, ciphertext.salt);

	            // Add IV to config
	            cfg.iv = derivedParams.iv;

	            // Decrypt
	            var plaintext = SerializableCipher.decrypt.call(this, cipher, ciphertext, derivedParams.key, cfg);

	            return plaintext;
	        }
	    });
	}());


}));

/***/ }),
/* 13 */
/***/ (function(module, exports) {

module.exports = function (it) {
  return typeof it === 'object' ? it !== null : typeof it === 'function';
};


/***/ }),
/* 14 */
/***/ (function(module, exports) {

var hasOwnProperty = {}.hasOwnProperty;

module.exports = function (it, key) {
  return hasOwnProperty.call(it, key);
};


/***/ }),
/* 15 */
/***/ (function(module, exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(11);
var IE8_DOM_DEFINE = __webpack_require__(198);
var anObject = __webpack_require__(16);
var toPrimitive = __webpack_require__(55);

var nativeDefineProperty = Object.defineProperty;

// `Object.defineProperty` method
// https://tc39.github.io/ecma262/#sec-object.defineproperty
exports.f = DESCRIPTORS ? nativeDefineProperty : function defineProperty(O, P, Attributes) {
  anObject(O);
  P = toPrimitive(P, true);
  anObject(Attributes);
  if (IE8_DOM_DEFINE) try {
    return nativeDefineProperty(O, P, Attributes);
  } catch (error) { /* empty */ }
  if ('get' in Attributes || 'set' in Attributes) throw TypeError('Accessors not supported');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};


/***/ }),
/* 16 */
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__(13);

module.exports = function (it) {
  if (!isObject(it)) {
    throw TypeError(String(it) + ' is not an object');
  } return it;
};


/***/ }),
/* 17 */
/***/ (function(module, exports) {

// https://github.com/zloirock/core-js/issues/86#issuecomment-115759028
var global = module.exports = typeof window != 'undefined' && window.Math == Math
  ? window : typeof self != 'undefined' && self.Math == Math ? self
  // eslint-disable-next-line no-new-func
  : Function('return this')();
if (typeof __g == 'number') __g = global; // eslint-disable-line no-undef


/***/ }),
/* 18 */
/***/ (function(module, exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(11);
var definePropertyModule = __webpack_require__(15);
var createPropertyDescriptor = __webpack_require__(42);

module.exports = DESCRIPTORS ? function (object, key, value) {
  return definePropertyModule.f(object, key, createPropertyDescriptor(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};


/***/ }),
/* 19 */
/***/ (function(module, exports, __webpack_require__) {

var requireObjectCoercible = __webpack_require__(34);

// `ToObject` abstract operation
// https://tc39.github.io/ecma262/#sec-toobject
module.exports = function (argument) {
  return Object(requireObjectCoercible(argument));
};


/***/ }),
/* 20 */
/***/ (function(module, exports, __webpack_require__) {

var bind = __webpack_require__(96);
var IndexedObject = __webpack_require__(92);
var toObject = __webpack_require__(19);
var toLength = __webpack_require__(8);
var arraySpeciesCreate = __webpack_require__(206);

var push = [].push;

// `Array.prototype.{ forEach, map, filter, some, every, find, findIndex }` methods implementation
var createMethod = function (TYPE) {
  var IS_MAP = TYPE == 1;
  var IS_FILTER = TYPE == 2;
  var IS_SOME = TYPE == 3;
  var IS_EVERY = TYPE == 4;
  var IS_FIND_INDEX = TYPE == 6;
  var NO_HOLES = TYPE == 5 || IS_FIND_INDEX;
  return function ($this, callbackfn, that, specificCreate) {
    var O = toObject($this);
    var self = IndexedObject(O);
    var boundFunction = bind(callbackfn, that, 3);
    var length = toLength(self.length);
    var index = 0;
    var create = specificCreate || arraySpeciesCreate;
    var target = IS_MAP ? create($this, length) : IS_FILTER ? create($this, 0) : undefined;
    var value, result;
    for (;length > index; index++) if (NO_HOLES || index in self) {
      value = self[index];
      result = boundFunction(value, index, O);
      if (TYPE) {
        if (IS_MAP) target[index] = result; // map
        else if (result) switch (TYPE) {
          case 3: return true;              // some
          case 5: return value;             // find
          case 6: return index;             // findIndex
          case 2: push.call(target, value); // filter
        } else if (IS_EVERY) return false;  // every
      }
    }
    return IS_FIND_INDEX ? -1 : IS_SOME || IS_EVERY ? IS_EVERY : target;
  };
};

module.exports = {
  // `Array.prototype.forEach` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.foreach
  forEach: createMethod(0),
  // `Array.prototype.map` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.map
  map: createMethod(1),
  // `Array.prototype.filter` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.filter
  filter: createMethod(2),
  // `Array.prototype.some` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.some
  some: createMethod(3),
  // `Array.prototype.every` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.every
  every: createMethod(4),
  // `Array.prototype.find` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.find
  find: createMethod(5),
  // `Array.prototype.findIndex` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.findIndex
  findIndex: createMethod(6)
};


/***/ }),
/* 21 */
/***/ (function(module, exports, __webpack_require__) {

var store = __webpack_require__(181)('wks');
var uid = __webpack_require__(131);
var Symbol = __webpack_require__(17).Symbol;
var USE_SYMBOL = typeof Symbol == 'function';

var $exports = module.exports = function (name) {
  return store[name] || (store[name] =
    USE_SYMBOL && Symbol[name] || (USE_SYMBOL ? Symbol : uid)('Symbol.' + name));
};

$exports.store = store;


/***/ }),
/* 22 */
/***/ (function(module, exports) {

module.exports = function (it) {
  return typeof it === 'object' ? it !== null : typeof it === 'function';
};


/***/ }),
/* 23 */
/***/ (function(module, exports, __webpack_require__) {

// Thank's IE8 for his funny defineProperty
module.exports = !__webpack_require__(52)(function () {
  return Object.defineProperty({}, 'a', { get: function () { return 7; } }).a != 7;
});


/***/ }),
/* 24 */
/***/ (function(module, exports) {

var g;

// This works in non-strict mode
g = (function() {
	return this;
})();

try {
	// This works if eval is allowed (see CSP)
	g = g || new Function("return this")();
} catch (e) {
	// This works if the window reference is available
	if (typeof window === "object") g = window;
}

// g can still be undefined, but nothing to do about it...
// We return undefined, instead of nothing here, so it's
// easier to handle this case. if(!global) { ...}

module.exports = g;


/***/ }),
/* 25 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var createNonEnumerableProperty = __webpack_require__(18);
var has = __webpack_require__(14);
var setGlobal = __webpack_require__(139);
var inspectSource = __webpack_require__(140);
var InternalStateModule = __webpack_require__(35);

var getInternalState = InternalStateModule.get;
var enforceInternalState = InternalStateModule.enforce;
var TEMPLATE = String(String).split('String');

(module.exports = function (O, key, value, options) {
  var unsafe = options ? !!options.unsafe : false;
  var simple = options ? !!options.enumerable : false;
  var noTargetGet = options ? !!options.noTargetGet : false;
  if (typeof value == 'function') {
    if (typeof key == 'string' && !has(value, 'name')) createNonEnumerableProperty(value, 'name', key);
    enforceInternalState(value).source = TEMPLATE.join(typeof key == 'string' ? key : '');
  }
  if (O === global) {
    if (simple) O[key] = value;
    else setGlobal(key, value);
    return;
  } else if (!unsafe) {
    delete O[key];
  } else if (!noTargetGet && O[key]) {
    simple = true;
  }
  if (simple) O[key] = value;
  else createNonEnumerableProperty(O, key, value);
// add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
})(Function.prototype, 'toString', function toString() {
  return typeof this == 'function' && getInternalState(this).source || inspectSource(this);
});


/***/ }),
/* 26 */
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__(29);
var IE8_DOM_DEFINE = __webpack_require__(254);
var toPrimitive = __webpack_require__(175);
var dP = Object.defineProperty;

exports.f = __webpack_require__(23) ? Object.defineProperty : function defineProperty(O, P, Attributes) {
  anObject(O);
  P = toPrimitive(P, true);
  anObject(Attributes);
  if (IE8_DOM_DEFINE) try {
    return dP(O, P, Attributes);
  } catch (e) { /* empty */ }
  if ('get' in Attributes || 'set' in Attributes) throw TypeError('Accessors not supported!');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};


/***/ }),
/* 27 */
/***/ (function(module, exports, __webpack_require__) {

// toObject with fallback for non-array-like ES3 strings
var IndexedObject = __webpack_require__(92);
var requireObjectCoercible = __webpack_require__(34);

module.exports = function (it) {
  return IndexedObject(requireObjectCoercible(it));
};


/***/ }),
/* 28 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var toIndexedObject = __webpack_require__(27);
var addToUnscopables = __webpack_require__(149);
var Iterators = __webpack_require__(97);
var InternalStateModule = __webpack_require__(35);
var defineIterator = __webpack_require__(216);

var ARRAY_ITERATOR = 'Array Iterator';
var setInternalState = InternalStateModule.set;
var getInternalState = InternalStateModule.getterFor(ARRAY_ITERATOR);

// `Array.prototype.entries` method
// https://tc39.github.io/ecma262/#sec-array.prototype.entries
// `Array.prototype.keys` method
// https://tc39.github.io/ecma262/#sec-array.prototype.keys
// `Array.prototype.values` method
// https://tc39.github.io/ecma262/#sec-array.prototype.values
// `Array.prototype[@@iterator]` method
// https://tc39.github.io/ecma262/#sec-array.prototype-@@iterator
// `CreateArrayIterator` internal method
// https://tc39.github.io/ecma262/#sec-createarrayiterator
module.exports = defineIterator(Array, 'Array', function (iterated, kind) {
  setInternalState(this, {
    type: ARRAY_ITERATOR,
    target: toIndexedObject(iterated), // target
    index: 0,                          // next index
    kind: kind                         // kind
  });
// `%ArrayIteratorPrototype%.next` method
// https://tc39.github.io/ecma262/#sec-%arrayiteratorprototype%.next
}, function () {
  var state = getInternalState(this);
  var target = state.target;
  var kind = state.kind;
  var index = state.index++;
  if (!target || index >= target.length) {
    state.target = undefined;
    return { value: undefined, done: true };
  }
  if (kind == 'keys') return { value: index, done: false };
  if (kind == 'values') return { value: target[index], done: false };
  return { value: [index, target[index]], done: false };
}, 'values');

// argumentsList[@@iterator] is %ArrayProto_values%
// https://tc39.github.io/ecma262/#sec-createunmappedargumentsobject
// https://tc39.github.io/ecma262/#sec-createmappedargumentsobject
Iterators.Arguments = Iterators.Array;

// https://tc39.github.io/ecma262/#sec-array.prototype-@@unscopables
addToUnscopables('keys');
addToUnscopables('values');
addToUnscopables('entries');


/***/ }),
/* 29 */
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__(22);
module.exports = function (it) {
  if (!isObject(it)) throw TypeError(it + ' is not an object!');
  return it;
};


/***/ }),
/* 30 */
/***/ (function(module, exports) {

var toString = {}.toString;

module.exports = function (it) {
  return toString.call(it).slice(8, -1);
};


/***/ }),
/* 31 */
/***/ (function(module, exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(11);
var fails = __webpack_require__(4);
var has = __webpack_require__(14);

var defineProperty = Object.defineProperty;
var cache = {};

var thrower = function (it) { throw it; };

module.exports = function (METHOD_NAME, options) {
  if (has(cache, METHOD_NAME)) return cache[METHOD_NAME];
  if (!options) options = {};
  var method = [][METHOD_NAME];
  var ACCESSORS = has(options, 'ACCESSORS') ? options.ACCESSORS : false;
  var argument0 = has(options, 0) ? options[0] : thrower;
  var argument1 = has(options, 1) ? options[1] : undefined;

  return cache[METHOD_NAME] = !!method && !fails(function () {
    if (ACCESSORS && !DESCRIPTORS) return true;
    var O = { length: -1 };

    if (ACCESSORS) defineProperty(O, 1, { enumerable: true, get: thrower });
    else O[1] = 1;

    method.call(O, argument0, argument1);
  });
};


/***/ }),
/* 32 */
/***/ (function(module, exports, __webpack_require__) {

var TO_STRING_TAG_SUPPORT = __webpack_require__(152);
var redefine = __webpack_require__(25);
var toString = __webpack_require__(328);

// `Object.prototype.toString` method
// https://tc39.github.io/ecma262/#sec-object.prototype.tostring
if (!TO_STRING_TAG_SUPPORT) {
  redefine(Object.prototype, 'toString', toString, { unsafe: true });
}


/***/ }),
/* 33 */
/***/ (function(module, exports, __webpack_require__) {

// optional / simple context binding
var aFunction = __webpack_require__(101);
module.exports = function (fn, that, length) {
  aFunction(fn);
  if (that === undefined) return fn;
  switch (length) {
    case 1: return function (a) {
      return fn.call(that, a);
    };
    case 2: return function (a, b) {
      return fn.call(that, a, b);
    };
    case 3: return function (a, b, c) {
      return fn.call(that, a, b, c);
    };
  }
  return function (/* ...args */) {
    return fn.apply(that, arguments);
  };
};


/***/ }),
/* 34 */
/***/ (function(module, exports) {

// `RequireObjectCoercible` abstract operation
// https://tc39.github.io/ecma262/#sec-requireobjectcoercible
module.exports = function (it) {
  if (it == undefined) throw TypeError("Can't call method on " + it);
  return it;
};


/***/ }),
/* 35 */
/***/ (function(module, exports, __webpack_require__) {

var NATIVE_WEAK_MAP = __webpack_require__(308);
var global = __webpack_require__(3);
var isObject = __webpack_require__(13);
var createNonEnumerableProperty = __webpack_require__(18);
var objectHas = __webpack_require__(14);
var sharedKey = __webpack_require__(111);
var hiddenKeys = __webpack_require__(113);

var WeakMap = global.WeakMap;
var set, get, has;

var enforce = function (it) {
  return has(it) ? get(it) : set(it, {});
};

var getterFor = function (TYPE) {
  return function (it) {
    var state;
    if (!isObject(it) || (state = get(it)).type !== TYPE) {
      throw TypeError('Incompatible receiver, ' + TYPE + ' required');
    } return state;
  };
};

if (NATIVE_WEAK_MAP) {
  var store = new WeakMap();
  var wmget = store.get;
  var wmhas = store.has;
  var wmset = store.set;
  set = function (it, metadata) {
    wmset.call(store, it, metadata);
    return metadata;
  };
  get = function (it) {
    return wmget.call(store, it) || {};
  };
  has = function (it) {
    return wmhas.call(store, it);
  };
} else {
  var STATE = sharedKey('state');
  hiddenKeys[STATE] = true;
  set = function (it, metadata) {
    createNonEnumerableProperty(it, STATE, metadata);
    return metadata;
  };
  get = function (it) {
    return objectHas(it, STATE) ? it[STATE] : {};
  };
  has = function (it) {
    return objectHas(it, STATE);
  };
}

module.exports = {
  set: set,
  get: get,
  has: has,
  enforce: enforce,
  getterFor: getterFor
};


/***/ }),
/* 36 */
/***/ (function(module, exports, __webpack_require__) {

var path = __webpack_require__(114);
var global = __webpack_require__(3);

var aFunction = function (variable) {
  return typeof variable == 'function' ? variable : undefined;
};

module.exports = function (namespace, method) {
  return arguments.length < 2 ? aFunction(path[namespace]) || aFunction(global[namespace])
    : path[namespace] && path[namespace][method] || global[namespace] && global[namespace][method];
};


/***/ }),
/* 37 */
/***/ (function(module, exports) {

var ceil = Math.ceil;
var floor = Math.floor;

// `ToInteger` abstract operation
// https://tc39.github.io/ecma262/#sec-tointeger
module.exports = function (argument) {
  return isNaN(argument = +argument) ? 0 : (argument > 0 ? floor : ceil)(argument);
};


/***/ }),
/* 38 */
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__(16);
var aFunction = __webpack_require__(44);
var wellKnownSymbol = __webpack_require__(6);

var SPECIES = wellKnownSymbol('species');

// `SpeciesConstructor` abstract operation
// https://tc39.github.io/ecma262/#sec-speciesconstructor
module.exports = function (O, defaultConstructor) {
  var C = anObject(O).constructor;
  var S;
  return C === undefined || (S = anObject(C)[SPECIES]) == undefined ? defaultConstructor : aFunction(S);
};


/***/ }),
/* 39 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(process) {/* eslint-disable node/no-deprecated-api */



var buffer = __webpack_require__(10)
var Buffer = buffer.Buffer

var safer = {}

var key

for (key in buffer) {
  if (!buffer.hasOwnProperty(key)) continue
  if (key === 'SlowBuffer' || key === 'Buffer') continue
  safer[key] = buffer[key]
}

var Safer = safer.Buffer = {}
for (key in Buffer) {
  if (!Buffer.hasOwnProperty(key)) continue
  if (key === 'allocUnsafe' || key === 'allocUnsafeSlow') continue
  Safer[key] = Buffer[key]
}

safer.Buffer.prototype = Buffer.prototype

if (!Safer.from || Safer.from === Uint8Array.from) {
  Safer.from = function (value, encodingOrOffset, length) {
    if (typeof value === 'number') {
      throw new TypeError('The "value" argument must not be of type number. Received type ' + typeof value)
    }
    if (value && typeof value.length === 'undefined') {
      throw new TypeError('The first argument must be one of type string, Buffer, ArrayBuffer, Array, or Array-like Object. Received type ' + typeof value)
    }
    return Buffer(value, encodingOrOffset, length)
  }
}

if (!Safer.alloc) {
  Safer.alloc = function (size, fill, encoding) {
    if (typeof size !== 'number') {
      throw new TypeError('The "size" argument must be of type number. Received type ' + typeof size)
    }
    if (size < 0 || size >= 2 * (1 << 30)) {
      throw new RangeError('The value "' + size + '" is invalid for option "size"')
    }
    var buf = Buffer(size)
    if (!fill || fill.length === 0) {
      buf.fill(0)
    } else if (typeof encoding === 'string') {
      buf.fill(fill, encoding)
    } else {
      buf.fill(fill)
    }
    return buf
  }
}

if (!safer.kStringMaxLength) {
  try {
    safer.kStringMaxLength = process.binding('buffer').kStringMaxLength
  } catch (e) {
    // we can't determine kStringMaxLength in environments where process.binding
    // is unsupported, so let's not set it
  }
}

if (!safer.constants) {
  safer.constants = {
    MAX_LENGTH: safer.kMaxLength
  }
  if (safer.kStringMaxLength) {
    safer.constants.MAX_STRING_LENGTH = safer.kStringMaxLength
  }
}

module.exports = safer

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(47)))

/***/ }),
/* 40 */
/***/ (function(module, exports, __webpack_require__) {

// Generated by CoffeeScript 1.7.1
(function() {
  var NumberT, PropertyDescriptor;

  NumberT = __webpack_require__(89).Number;

  exports.resolveLength = function(length, stream, parent) {
    var res;
    if (typeof length === 'number') {
      res = length;
    } else if (typeof length === 'function') {
      res = length.call(parent, parent);
    } else if (parent && typeof length === 'string') {
      res = parent[length];
    } else if (stream && length instanceof NumberT) {
      res = length.decode(stream);
    }
    if (isNaN(res)) {
      throw new Error('Not a fixed size');
    }
    return res;
  };

  PropertyDescriptor = (function() {
    function PropertyDescriptor(opts) {
      var key, val;
      if (opts == null) {
        opts = {};
      }
      this.enumerable = true;
      this.configurable = true;
      for (key in opts) {
        val = opts[key];
        this[key] = val;
      }
    }

    return PropertyDescriptor;

  })();

  exports.PropertyDescriptor = PropertyDescriptor;

}).call(this);


/***/ }),
/* 41 */
/***/ (function(module, exports, __webpack_require__) {

var dP = __webpack_require__(26);
var createDesc = __webpack_require__(100);
module.exports = __webpack_require__(23) ? function (object, key, value) {
  return dP.f(object, key, createDesc(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};


/***/ }),
/* 42 */
/***/ (function(module, exports) {

module.exports = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};


/***/ }),
/* 43 */
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__(37);

var max = Math.max;
var min = Math.min;

// Helper for a popular repeating case of the spec:
// Let integer be ? ToInteger(index).
// If integer < 0, let result be max((length + integer), 0); else let result be min(integer, length).
module.exports = function (index, length) {
  var integer = toInteger(index);
  return integer < 0 ? max(integer + length, 0) : min(integer, length);
};


/***/ }),
/* 44 */
/***/ (function(module, exports) {

module.exports = function (it) {
  if (typeof it != 'function') {
    throw TypeError(String(it) + ' is not a function');
  } return it;
};


/***/ }),
/* 45 */
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__(16);
var aPossiblePrototype = __webpack_require__(317);

// `Object.setPrototypeOf` method
// https://tc39.github.io/ecma262/#sec-object.setprototypeof
// Works with __proto__ only. Old v8 can't work with null proto objects.
/* eslint-disable no-proto */
module.exports = Object.setPrototypeOf || ('__proto__' in {} ? function () {
  var CORRECT_SETTER = false;
  var test = {};
  var setter;
  try {
    setter = Object.getOwnPropertyDescriptor(Object.prototype, '__proto__').set;
    setter.call(test, []);
    CORRECT_SETTER = test instanceof Array;
  } catch (error) { /* empty */ }
  return function setPrototypeOf(O, proto) {
    anObject(O);
    aPossiblePrototype(proto);
    if (CORRECT_SETTER) setter.call(O, proto);
    else O.__proto__ = proto;
    return O;
  };
}() : undefined);


/***/ }),
/* 46 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var isObject = __webpack_require__(13);
var isArray = __webpack_require__(115);
var toAbsoluteIndex = __webpack_require__(43);
var toLength = __webpack_require__(8);
var toIndexedObject = __webpack_require__(27);
var createProperty = __webpack_require__(146);
var wellKnownSymbol = __webpack_require__(6);
var arrayMethodHasSpeciesSupport = __webpack_require__(116);
var arrayMethodUsesToLength = __webpack_require__(31);

var HAS_SPECIES_SUPPORT = arrayMethodHasSpeciesSupport('slice');
var USES_TO_LENGTH = arrayMethodUsesToLength('slice', { ACCESSORS: true, 0: 0, 1: 2 });

var SPECIES = wellKnownSymbol('species');
var nativeSlice = [].slice;
var max = Math.max;

// `Array.prototype.slice` method
// https://tc39.github.io/ecma262/#sec-array.prototype.slice
// fallback for not array-like ES3 strings and DOM objects
$({ target: 'Array', proto: true, forced: !HAS_SPECIES_SUPPORT || !USES_TO_LENGTH }, {
  slice: function slice(start, end) {
    var O = toIndexedObject(this);
    var length = toLength(O.length);
    var k = toAbsoluteIndex(start, length);
    var fin = toAbsoluteIndex(end === undefined ? length : end, length);
    // inline `ArraySpeciesCreate` for usage native `Array#slice` where it's possible
    var Constructor, result, n;
    if (isArray(O)) {
      Constructor = O.constructor;
      // cross-realm fallback
      if (typeof Constructor == 'function' && (Constructor === Array || isArray(Constructor.prototype))) {
        Constructor = undefined;
      } else if (isObject(Constructor)) {
        Constructor = Constructor[SPECIES];
        if (Constructor === null) Constructor = undefined;
      }
      if (Constructor === Array || Constructor === undefined) {
        return nativeSlice.call(O, k, fin);
      }
    }
    result = new (Constructor === undefined ? Array : Constructor)(max(fin - k, 0));
    for (n = 0; k < fin; k++, n++) if (k in O) createProperty(result, n, O[k]);
    result.length = n;
    return result;
  }
});


/***/ }),
/* 47 */
/***/ (function(module, exports) {

// shim for using process in browser
var process = module.exports = {};

// cached from whatever global is present so that test runners that stub it
// don't break things.  But we need to wrap it in a try catch in case it is
// wrapped in strict mode code which doesn't define any globals.  It's inside a
// function because try/catches deoptimize in certain engines.

var cachedSetTimeout;
var cachedClearTimeout;

function defaultSetTimout() {
    throw new Error('setTimeout has not been defined');
}
function defaultClearTimeout () {
    throw new Error('clearTimeout has not been defined');
}
(function () {
    try {
        if (typeof setTimeout === 'function') {
            cachedSetTimeout = setTimeout;
        } else {
            cachedSetTimeout = defaultSetTimout;
        }
    } catch (e) {
        cachedSetTimeout = defaultSetTimout;
    }
    try {
        if (typeof clearTimeout === 'function') {
            cachedClearTimeout = clearTimeout;
        } else {
            cachedClearTimeout = defaultClearTimeout;
        }
    } catch (e) {
        cachedClearTimeout = defaultClearTimeout;
    }
} ())
function runTimeout(fun) {
    if (cachedSetTimeout === setTimeout) {
        //normal enviroments in sane situations
        return setTimeout(fun, 0);
    }
    // if setTimeout wasn't available but was latter defined
    if ((cachedSetTimeout === defaultSetTimout || !cachedSetTimeout) && setTimeout) {
        cachedSetTimeout = setTimeout;
        return setTimeout(fun, 0);
    }
    try {
        // when when somebody has screwed with setTimeout but no I.E. maddness
        return cachedSetTimeout(fun, 0);
    } catch(e){
        try {
            // When we are in I.E. but the script has been evaled so I.E. doesn't trust the global object when called normally
            return cachedSetTimeout.call(null, fun, 0);
        } catch(e){
            // same as above but when it's a version of I.E. that must have the global object for 'this', hopfully our context correct otherwise it will throw a global error
            return cachedSetTimeout.call(this, fun, 0);
        }
    }


}
function runClearTimeout(marker) {
    if (cachedClearTimeout === clearTimeout) {
        //normal enviroments in sane situations
        return clearTimeout(marker);
    }
    // if clearTimeout wasn't available but was latter defined
    if ((cachedClearTimeout === defaultClearTimeout || !cachedClearTimeout) && clearTimeout) {
        cachedClearTimeout = clearTimeout;
        return clearTimeout(marker);
    }
    try {
        // when when somebody has screwed with setTimeout but no I.E. maddness
        return cachedClearTimeout(marker);
    } catch (e){
        try {
            // When we are in I.E. but the script has been evaled so I.E. doesn't  trust the global object when called normally
            return cachedClearTimeout.call(null, marker);
        } catch (e){
            // same as above but when it's a version of I.E. that must have the global object for 'this', hopfully our context correct otherwise it will throw a global error.
            // Some versions of I.E. have different rules for clearTimeout vs setTimeout
            return cachedClearTimeout.call(this, marker);
        }
    }



}
var queue = [];
var draining = false;
var currentQueue;
var queueIndex = -1;

function cleanUpNextTick() {
    if (!draining || !currentQueue) {
        return;
    }
    draining = false;
    if (currentQueue.length) {
        queue = currentQueue.concat(queue);
    } else {
        queueIndex = -1;
    }
    if (queue.length) {
        drainQueue();
    }
}

function drainQueue() {
    if (draining) {
        return;
    }
    var timeout = runTimeout(cleanUpNextTick);
    draining = true;

    var len = queue.length;
    while(len) {
        currentQueue = queue;
        queue = [];
        while (++queueIndex < len) {
            if (currentQueue) {
                currentQueue[queueIndex].run();
            }
        }
        queueIndex = -1;
        len = queue.length;
    }
    currentQueue = null;
    draining = false;
    runClearTimeout(timeout);
}

process.nextTick = function (fun) {
    var args = new Array(arguments.length - 1);
    if (arguments.length > 1) {
        for (var i = 1; i < arguments.length; i++) {
            args[i - 1] = arguments[i];
        }
    }
    queue.push(new Item(fun, args));
    if (queue.length === 1 && !draining) {
        runTimeout(drainQueue);
    }
};

// v8 likes predictible objects
function Item(fun, array) {
    this.fun = fun;
    this.array = array;
}
Item.prototype.run = function () {
    this.fun.apply(null, this.array);
};
process.title = 'browser';
process.browser = true;
process.env = {};
process.argv = [];
process.version = ''; // empty string to avoid regexp issues
process.versions = {};

function noop() {}

process.on = noop;
process.addListener = noop;
process.once = noop;
process.off = noop;
process.removeListener = noop;
process.removeAllListeners = noop;
process.emit = noop;
process.prependListener = noop;
process.prependOnceListener = noop;

process.listeners = function (name) { return [] }

process.binding = function (name) {
    throw new Error('process.binding is not supported');
};

process.cwd = function () { return '/' };
process.chdir = function (dir) {
    throw new Error('process.chdir is not supported');
};
process.umask = function() { return 0; };


/***/ }),
/* 48 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

// a duplex stream is just a stream that is both readable and writable.
// Since JS doesn't have multiple prototypal inheritance, this class
// prototypally inherits from Readable, and then parasitically from
// Writable.



/*<replacement>*/

var pna = __webpack_require__(123);
/*</replacement>*/

/*<replacement>*/
var objectKeys = Object.keys || function (obj) {
  var keys = [];
  for (var key in obj) {
    keys.push(key);
  }return keys;
};
/*</replacement>*/

module.exports = Duplex;

/*<replacement>*/
var util = Object.create(__webpack_require__(98));
util.inherits = __webpack_require__(86);
/*</replacement>*/

var Readable = __webpack_require__(240);
var Writable = __webpack_require__(165);

util.inherits(Duplex, Readable);

{
  // avoid scope creep, the keys array can then be collected
  var keys = objectKeys(Writable.prototype);
  for (var v = 0; v < keys.length; v++) {
    var method = keys[v];
    if (!Duplex.prototype[method]) Duplex.prototype[method] = Writable.prototype[method];
  }
}

function Duplex(options) {
  if (!(this instanceof Duplex)) return new Duplex(options);

  Readable.call(this, options);
  Writable.call(this, options);

  if (options && options.readable === false) this.readable = false;

  if (options && options.writable === false) this.writable = false;

  this.allowHalfOpen = true;
  if (options && options.allowHalfOpen === false) this.allowHalfOpen = false;

  this.once('end', onend);
}

Object.defineProperty(Duplex.prototype, 'writableHighWaterMark', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function () {
    return this._writableState.highWaterMark;
  }
});

// the no-half-open enforcer
function onend() {
  // if we allow half-open state, or if the writable side ended,
  // then we're ok.
  if (this.allowHalfOpen || this._writableState.ended) return;

  // no more data can be written.
  // But allow more writes to happen in this tick.
  pna.nextTick(onEndNT, this);
}

function onEndNT(self) {
  self.end();
}

Object.defineProperty(Duplex.prototype, 'destroyed', {
  get: function () {
    if (this._readableState === undefined || this._writableState === undefined) {
      return false;
    }
    return this._readableState.destroyed && this._writableState.destroyed;
  },
  set: function (value) {
    // we ignore the value if the stream
    // has not been initialized yet
    if (this._readableState === undefined || this._writableState === undefined) {
      return;
    }

    // backward compatibility, the user is explicitly
    // managing destroyed
    this._readableState.destroyed = value;
    this._writableState.destroyed = value;
  }
});

Duplex.prototype._destroy = function (err, cb) {
  this.push(null);
  this.end();

  pna.nextTick(cb, err);
};

/***/ }),
/* 49 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(167), __webpack_require__(168));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var Base = C_lib.Base;
	    var WordArray = C_lib.WordArray;
	    var C_algo = C.algo;
	    var MD5 = C_algo.MD5;

	    /**
	     * This key derivation function is meant to conform with EVP_BytesToKey.
	     * www.openssl.org/docs/crypto/EVP_BytesToKey.html
	     */
	    var EvpKDF = C_algo.EvpKDF = Base.extend({
	        /**
	         * Configuration options.
	         *
	         * @property {number} keySize The key size in words to generate. Default: 4 (128 bits)
	         * @property {Hasher} hasher The hash algorithm to use. Default: MD5
	         * @property {number} iterations The number of iterations to perform. Default: 1
	         */
	        cfg: Base.extend({
	            keySize: 128/32,
	            hasher: MD5,
	            iterations: 1
	        }),

	        /**
	         * Initializes a newly created key derivation function.
	         *
	         * @param {Object} cfg (Optional) The configuration options to use for the derivation.
	         *
	         * @example
	         *
	         *     var kdf = CryptoJS.algo.EvpKDF.create();
	         *     var kdf = CryptoJS.algo.EvpKDF.create({ keySize: 8 });
	         *     var kdf = CryptoJS.algo.EvpKDF.create({ keySize: 8, iterations: 1000 });
	         */
	        init: function (cfg) {
	            this.cfg = this.cfg.extend(cfg);
	        },

	        /**
	         * Derives a key from a password.
	         *
	         * @param {WordArray|string} password The password.
	         * @param {WordArray|string} salt A salt.
	         *
	         * @return {WordArray} The derived key.
	         *
	         * @example
	         *
	         *     var key = kdf.compute(password, salt);
	         */
	        compute: function (password, salt) {
	            // Shortcut
	            var cfg = this.cfg;

	            // Init hasher
	            var hasher = cfg.hasher.create();

	            // Initial values
	            var derivedKey = WordArray.create();

	            // Shortcuts
	            var derivedKeyWords = derivedKey.words;
	            var keySize = cfg.keySize;
	            var iterations = cfg.iterations;

	            // Generate key
	            while (derivedKeyWords.length < keySize) {
	                if (block) {
	                    hasher.update(block);
	                }
	                var block = hasher.update(password).finalize(salt);
	                hasher.reset();

	                // Iterations
	                for (var i = 1; i < iterations; i++) {
	                    block = hasher.finalize(block);
	                    hasher.reset();
	                }

	                derivedKey.concat(block);
	            }
	            derivedKey.sigBytes = keySize * 4;

	            return derivedKey;
	        }
	    });

	    /**
	     * Derives a key from a password.
	     *
	     * @param {WordArray|string} password The password.
	     * @param {WordArray|string} salt A salt.
	     * @param {Object} cfg (Optional) The configuration options to use for this computation.
	     *
	     * @return {WordArray} The derived key.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var key = CryptoJS.EvpKDF(password, salt);
	     *     var key = CryptoJS.EvpKDF(password, salt, { keySize: 8 });
	     *     var key = CryptoJS.EvpKDF(password, salt, { keySize: 8, iterations: 1000 });
	     */
	    C.EvpKDF = function (password, salt, cfg) {
	        return EvpKDF.create(cfg).compute(password, salt);
	    };
	}());


	return CryptoJS.EvpKDF;

}));

/***/ }),
/* 50 */
/***/ (function(module, exports, __webpack_require__) {

// to indexed object, toObject with fallback for non-array-like ES3 strings
var IObject = __webpack_require__(172);
var defined = __webpack_require__(173);
module.exports = function (it) {
  return IObject(defined(it));
};


/***/ }),
/* 51 */
/***/ (function(module, exports) {

var hasOwnProperty = {}.hasOwnProperty;
module.exports = function (it, key) {
  return hasOwnProperty.call(it, key);
};


/***/ }),
/* 52 */
/***/ (function(module, exports) {

module.exports = function (exec) {
  try {
    return !!exec();
  } catch (e) {
    return true;
  }
};


/***/ }),
/* 53 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(Buffer, __dirname) {

function VirtualFileSystem() {
	this.fileSystem = {};
	this.dataSystem = {};
}

VirtualFileSystem.prototype.existsSync = function (filename) {
	filename = fixFilename(filename);
	return typeof this.fileSystem[filename] !== 'undefined'
		|| typeof this.dataSystem[filename] !== 'undefined';
}

VirtualFileSystem.prototype.readFileSync = function (filename, options) {
	filename = fixFilename(filename);

	var dataContent = this.dataSystem[filename];
	if (typeof dataContent === 'string' && options === 'utf8') {
		return dataContent;
	}

	if (dataContent) {
		return new Buffer(dataContent, typeof dataContent === 'string' ? 'base64' : undefined);
	}

	var content = this.fileSystem[filename];
	if (content) {
		return content;
	}

	throw 'File \'' + filename + '\' not found in virtual file system';
};

VirtualFileSystem.prototype.writeFileSync = function (filename, content) {
	this.fileSystem[fixFilename(filename)] = content;
};

VirtualFileSystem.prototype.bindFS = function (data) {
	this.dataSystem = data || {};
};


function fixFilename(filename) {
	if (filename.indexOf(__dirname) === 0) {
		filename = filename.substring(__dirname.length);
	}

	if (filename.indexOf('/') === 0) {
		filename = filename.substring(1);
	}

	return filename;
}

module.exports = new VirtualFileSystem();

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer, "/"))

/***/ }),
/* 54 */
/***/ (function(module, exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(11);
var propertyIsEnumerableModule = __webpack_require__(110);
var createPropertyDescriptor = __webpack_require__(42);
var toIndexedObject = __webpack_require__(27);
var toPrimitive = __webpack_require__(55);
var has = __webpack_require__(14);
var IE8_DOM_DEFINE = __webpack_require__(198);

var nativeGetOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;

// `Object.getOwnPropertyDescriptor` method
// https://tc39.github.io/ecma262/#sec-object.getownpropertydescriptor
exports.f = DESCRIPTORS ? nativeGetOwnPropertyDescriptor : function getOwnPropertyDescriptor(O, P) {
  O = toIndexedObject(O);
  P = toPrimitive(P, true);
  if (IE8_DOM_DEFINE) try {
    return nativeGetOwnPropertyDescriptor(O, P);
  } catch (error) { /* empty */ }
  if (has(O, P)) return createPropertyDescriptor(!propertyIsEnumerableModule.f.call(O, P), O[P]);
};


/***/ }),
/* 55 */
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__(13);

// `ToPrimitive` abstract operation
// https://tc39.github.io/ecma262/#sec-toprimitive
// instead of the ES6 spec version, we didn't implement @@toPrimitive case
// and the second argument - flag - preferred type is a string
module.exports = function (input, PREFERRED_STRING) {
  if (!isObject(input)) return input;
  var fn, val;
  if (PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject(val = fn.call(input))) return val;
  if (typeof (fn = input.valueOf) == 'function' && !isObject(val = fn.call(input))) return val;
  if (!PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject(val = fn.call(input))) return val;
  throw TypeError("Can't convert object to primitive value");
};


/***/ }),
/* 56 */
/***/ (function(module, exports) {

module.exports = false;


/***/ }),
/* 57 */
/***/ (function(module, exports, __webpack_require__) {

var internalObjectKeys = __webpack_require__(201);
var enumBugKeys = __webpack_require__(142);

var hiddenKeys = enumBugKeys.concat('length', 'prototype');

// `Object.getOwnPropertyNames` method
// https://tc39.github.io/ecma262/#sec-object.getownpropertynames
exports.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return internalObjectKeys(O, hiddenKeys);
};


/***/ }),
/* 58 */
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__(16);
var defineProperties = __webpack_require__(310);
var enumBugKeys = __webpack_require__(142);
var hiddenKeys = __webpack_require__(113);
var html = __webpack_require__(203);
var documentCreateElement = __webpack_require__(138);
var sharedKey = __webpack_require__(111);

var GT = '>';
var LT = '<';
var PROTOTYPE = 'prototype';
var SCRIPT = 'script';
var IE_PROTO = sharedKey('IE_PROTO');

var EmptyConstructor = function () { /* empty */ };

var scriptTag = function (content) {
  return LT + SCRIPT + GT + content + LT + '/' + SCRIPT + GT;
};

// Create object with fake `null` prototype: use ActiveX Object with cleared prototype
var NullProtoObjectViaActiveX = function (activeXDocument) {
  activeXDocument.write(scriptTag(''));
  activeXDocument.close();
  var temp = activeXDocument.parentWindow.Object;
  activeXDocument = null; // avoid memory leak
  return temp;
};

// Create object with fake `null` prototype: use iframe Object with cleared prototype
var NullProtoObjectViaIFrame = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = documentCreateElement('iframe');
  var JS = 'java' + SCRIPT + ':';
  var iframeDocument;
  iframe.style.display = 'none';
  html.appendChild(iframe);
  // https://github.com/zloirock/core-js/issues/475
  iframe.src = String(JS);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(scriptTag('document.F=Object'));
  iframeDocument.close();
  return iframeDocument.F;
};

// Check for document.domain and active x support
// No need to use active x approach when document.domain is not set
// see https://github.com/es-shims/es5-shim/issues/150
// variation of https://github.com/kitcambridge/es5-shim/commit/4f738ac066346
// avoid IE GC bug
var activeXDocument;
var NullProtoObject = function () {
  try {
    /* global ActiveXObject */
    activeXDocument = document.domain && new ActiveXObject('htmlfile');
  } catch (error) { /* ignore */ }
  NullProtoObject = activeXDocument ? NullProtoObjectViaActiveX(activeXDocument) : NullProtoObjectViaIFrame();
  var length = enumBugKeys.length;
  while (length--) delete NullProtoObject[PROTOTYPE][enumBugKeys[length]];
  return NullProtoObject();
};

hiddenKeys[IE_PROTO] = true;

// `Object.create` method
// https://tc39.github.io/ecma262/#sec-object.create
module.exports = Object.create || function create(O, Properties) {
  var result;
  if (O !== null) {
    EmptyConstructor[PROTOTYPE] = anObject(O);
    result = new EmptyConstructor();
    EmptyConstructor[PROTOTYPE] = null;
    // add "__proto__" for Object.getPrototypeOf polyfill
    result[IE_PROTO] = O;
  } else result = NullProtoObject();
  return Properties === undefined ? result : defineProperties(result, Properties);
};


/***/ }),
/* 59 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var fails = __webpack_require__(4);

module.exports = function (METHOD_NAME, argument) {
  var method = [][METHOD_NAME];
  return !!method && fails(function () {
    // eslint-disable-next-line no-useless-call,no-throw-literal
    method.call(null, argument || function () { throw 1; }, 1);
  });
};


/***/ }),
/* 60 */
/***/ (function(module, exports, __webpack_require__) {

var has = __webpack_require__(14);
var toObject = __webpack_require__(19);
var sharedKey = __webpack_require__(111);
var CORRECT_PROTOTYPE_GETTER = __webpack_require__(218);

var IE_PROTO = sharedKey('IE_PROTO');
var ObjectPrototype = Object.prototype;

// `Object.getPrototypeOf` method
// https://tc39.github.io/ecma262/#sec-object.getprototypeof
module.exports = CORRECT_PROTOTYPE_GETTER ? Object.getPrototypeOf : function (O) {
  O = toObject(O);
  if (has(O, IE_PROTO)) return O[IE_PROTO];
  if (typeof O.constructor == 'function' && O instanceof O.constructor) {
    return O.constructor.prototype;
  } return O instanceof Object ? ObjectPrototype : null;
};


/***/ }),
/* 61 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var fails = __webpack_require__(4);
var ArrayBufferModule = __webpack_require__(154);
var anObject = __webpack_require__(16);
var toAbsoluteIndex = __webpack_require__(43);
var toLength = __webpack_require__(8);
var speciesConstructor = __webpack_require__(38);

var ArrayBuffer = ArrayBufferModule.ArrayBuffer;
var DataView = ArrayBufferModule.DataView;
var nativeArrayBufferSlice = ArrayBuffer.prototype.slice;

var INCORRECT_SLICE = fails(function () {
  return !new ArrayBuffer(2).slice(1, undefined).byteLength;
});

// `ArrayBuffer.prototype.slice` method
// https://tc39.github.io/ecma262/#sec-arraybuffer.prototype.slice
$({ target: 'ArrayBuffer', proto: true, unsafe: true, forced: INCORRECT_SLICE }, {
  slice: function slice(start, end) {
    if (nativeArrayBufferSlice !== undefined && end === undefined) {
      return nativeArrayBufferSlice.call(anObject(this), start); // FF fix
    }
    var length = anObject(this).byteLength;
    var first = toAbsoluteIndex(start, length);
    var fin = toAbsoluteIndex(end === undefined ? length : end, length);
    var result = new (speciesConstructor(this, ArrayBuffer))(toLength(fin - first));
    var viewSource = new DataView(this);
    var viewTarget = new DataView(result);
    var index = 0;
    while (first < fin) {
      viewTarget.setUint8(index++, viewSource.getUint8(first++));
    } return result;
  }
});


/***/ }),
/* 62 */
/***/ (function(module, exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(236);

// `Uint8Array` constructor
// https://tc39.github.io/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Uint8', function (init) {
  return function Uint8Array(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
});


/***/ }),
/* 63 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $copyWithin = __webpack_require__(338);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.copyWithin` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.copywithin
exportTypedArrayMethod('copyWithin', function copyWithin(target, start /* , end */) {
  return $copyWithin.call(aTypedArray(this), target, start, arguments.length > 2 ? arguments[2] : undefined);
});


/***/ }),
/* 64 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $every = __webpack_require__(20).every;

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.every` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.every
exportTypedArrayMethod('every', function every(callbackfn /* , thisArg */) {
  return $every(aTypedArray(this), callbackfn, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),
/* 65 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $fill = __webpack_require__(148);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.fill` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.fill
// eslint-disable-next-line no-unused-vars
exportTypedArrayMethod('fill', function fill(value /* , start, end */) {
  return $fill.apply(aTypedArray(this), arguments);
});


/***/ }),
/* 66 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $filter = __webpack_require__(20).filter;
var speciesConstructor = __webpack_require__(38);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var aTypedArrayConstructor = ArrayBufferViewCore.aTypedArrayConstructor;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.filter` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.filter
exportTypedArrayMethod('filter', function filter(callbackfn /* , thisArg */) {
  var list = $filter(aTypedArray(this), callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  var C = speciesConstructor(this, this.constructor);
  var index = 0;
  var length = list.length;
  var result = new (aTypedArrayConstructor(C))(length);
  while (length > index) result[index] = list[index++];
  return result;
});


/***/ }),
/* 67 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $find = __webpack_require__(20).find;

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.find` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.find
exportTypedArrayMethod('find', function find(predicate /* , thisArg */) {
  return $find(aTypedArray(this), predicate, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),
/* 68 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $findIndex = __webpack_require__(20).findIndex;

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.findIndex` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.findindex
exportTypedArrayMethod('findIndex', function findIndex(predicate /* , thisArg */) {
  return $findIndex(aTypedArray(this), predicate, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),
/* 69 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $forEach = __webpack_require__(20).forEach;

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.forEach` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.foreach
exportTypedArrayMethod('forEach', function forEach(callbackfn /* , thisArg */) {
  $forEach(aTypedArray(this), callbackfn, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),
/* 70 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $includes = __webpack_require__(93).includes;

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.includes` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.includes
exportTypedArrayMethod('includes', function includes(searchElement /* , fromIndex */) {
  return $includes(aTypedArray(this), searchElement, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),
/* 71 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $indexOf = __webpack_require__(93).indexOf;

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.indexOf` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.indexof
exportTypedArrayMethod('indexOf', function indexOf(searchElement /* , fromIndex */) {
  return $indexOf(aTypedArray(this), searchElement, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),
/* 72 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(3);
var ArrayBufferViewCore = __webpack_require__(5);
var ArrayIterators = __webpack_require__(28);
var wellKnownSymbol = __webpack_require__(6);

var ITERATOR = wellKnownSymbol('iterator');
var Uint8Array = global.Uint8Array;
var arrayValues = ArrayIterators.values;
var arrayKeys = ArrayIterators.keys;
var arrayEntries = ArrayIterators.entries;
var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var nativeTypedArrayIterator = Uint8Array && Uint8Array.prototype[ITERATOR];

var CORRECT_ITER_NAME = !!nativeTypedArrayIterator
  && (nativeTypedArrayIterator.name == 'values' || nativeTypedArrayIterator.name == undefined);

var typedArrayValues = function values() {
  return arrayValues.call(aTypedArray(this));
};

// `%TypedArray%.prototype.entries` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.entries
exportTypedArrayMethod('entries', function entries() {
  return arrayEntries.call(aTypedArray(this));
});
// `%TypedArray%.prototype.keys` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.keys
exportTypedArrayMethod('keys', function keys() {
  return arrayKeys.call(aTypedArray(this));
});
// `%TypedArray%.prototype.values` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.values
exportTypedArrayMethod('values', typedArrayValues, !CORRECT_ITER_NAME);
// `%TypedArray%.prototype[@@iterator]` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype-@@iterator
exportTypedArrayMethod(ITERATOR, typedArrayValues, !CORRECT_ITER_NAME);


/***/ }),
/* 73 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var $join = [].join;

// `%TypedArray%.prototype.join` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.join
// eslint-disable-next-line no-unused-vars
exportTypedArrayMethod('join', function join(separator) {
  return $join.apply(aTypedArray(this), arguments);
});


/***/ }),
/* 74 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $lastIndexOf = __webpack_require__(339);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.lastIndexOf` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.lastindexof
// eslint-disable-next-line no-unused-vars
exportTypedArrayMethod('lastIndexOf', function lastIndexOf(searchElement /* , fromIndex */) {
  return $lastIndexOf.apply(aTypedArray(this), arguments);
});


/***/ }),
/* 75 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $map = __webpack_require__(20).map;
var speciesConstructor = __webpack_require__(38);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var aTypedArrayConstructor = ArrayBufferViewCore.aTypedArrayConstructor;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.map` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.map
exportTypedArrayMethod('map', function map(mapfn /* , thisArg */) {
  return $map(aTypedArray(this), mapfn, arguments.length > 1 ? arguments[1] : undefined, function (O, length) {
    return new (aTypedArrayConstructor(speciesConstructor(O, O.constructor)))(length);
  });
});


/***/ }),
/* 76 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $reduce = __webpack_require__(238).left;

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.reduce` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.reduce
exportTypedArrayMethod('reduce', function reduce(callbackfn /* , initialValue */) {
  return $reduce(aTypedArray(this), callbackfn, arguments.length, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),
/* 77 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $reduceRight = __webpack_require__(238).right;

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.reduceRicht` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.reduceright
exportTypedArrayMethod('reduceRight', function reduceRight(callbackfn /* , initialValue */) {
  return $reduceRight(aTypedArray(this), callbackfn, arguments.length, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),
/* 78 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var floor = Math.floor;

// `%TypedArray%.prototype.reverse` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.reverse
exportTypedArrayMethod('reverse', function reverse() {
  var that = this;
  var length = aTypedArray(that).length;
  var middle = floor(length / 2);
  var index = 0;
  var value;
  while (index < middle) {
    value = that[index];
    that[index++] = that[--length];
    that[length] = value;
  } return that;
});


/***/ }),
/* 79 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var toLength = __webpack_require__(8);
var toOffset = __webpack_require__(237);
var toObject = __webpack_require__(19);
var fails = __webpack_require__(4);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

var FORCED = fails(function () {
  // eslint-disable-next-line no-undef
  new Int8Array(1).set({});
});

// `%TypedArray%.prototype.set` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.set
exportTypedArrayMethod('set', function set(arrayLike /* , offset */) {
  aTypedArray(this);
  var offset = toOffset(arguments.length > 1 ? arguments[1] : undefined, 1);
  var length = this.length;
  var src = toObject(arrayLike);
  var len = toLength(src.length);
  var index = 0;
  if (len + offset > length) throw RangeError('Wrong length');
  while (index < len) this[offset + index] = src[index++];
}, FORCED);


/***/ }),
/* 80 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var speciesConstructor = __webpack_require__(38);
var fails = __webpack_require__(4);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var aTypedArrayConstructor = ArrayBufferViewCore.aTypedArrayConstructor;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var $slice = [].slice;

var FORCED = fails(function () {
  // eslint-disable-next-line no-undef
  new Int8Array(1).slice();
});

// `%TypedArray%.prototype.slice` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.slice
exportTypedArrayMethod('slice', function slice(start, end) {
  var list = $slice.call(aTypedArray(this), start, end);
  var C = speciesConstructor(this, this.constructor);
  var index = 0;
  var length = list.length;
  var result = new (aTypedArrayConstructor(C))(length);
  while (length > index) result[index] = list[index++];
  return result;
}, FORCED);


/***/ }),
/* 81 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var $some = __webpack_require__(20).some;

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.some` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.some
exportTypedArrayMethod('some', function some(callbackfn /* , thisArg */) {
  return $some(aTypedArray(this), callbackfn, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),
/* 82 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var $sort = [].sort;

// `%TypedArray%.prototype.sort` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.sort
exportTypedArrayMethod('sort', function sort(comparefn) {
  return $sort.call(aTypedArray(this), comparefn);
});


/***/ }),
/* 83 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(5);
var toLength = __webpack_require__(8);
var toAbsoluteIndex = __webpack_require__(43);
var speciesConstructor = __webpack_require__(38);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.subarray` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.subarray
exportTypedArrayMethod('subarray', function subarray(begin, end) {
  var O = aTypedArray(this);
  var length = O.length;
  var beginIndex = toAbsoluteIndex(begin, length);
  return new (speciesConstructor(O, O.constructor))(
    O.buffer,
    O.byteOffset + beginIndex * O.BYTES_PER_ELEMENT,
    toLength((end === undefined ? length : toAbsoluteIndex(end, length)) - beginIndex)
  );
});


/***/ }),
/* 84 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(3);
var ArrayBufferViewCore = __webpack_require__(5);
var fails = __webpack_require__(4);

var Int8Array = global.Int8Array;
var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var $toLocaleString = [].toLocaleString;
var $slice = [].slice;

// iOS Safari 6.x fails here
var TO_LOCALE_STRING_BUG = !!Int8Array && fails(function () {
  $toLocaleString.call(new Int8Array(1));
});

var FORCED = fails(function () {
  return [1, 2].toLocaleString() != new Int8Array([1, 2]).toLocaleString();
}) || !fails(function () {
  Int8Array.prototype.toLocaleString.call([1, 2]);
});

// `%TypedArray%.prototype.toLocaleString` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.tolocalestring
exportTypedArrayMethod('toLocaleString', function toLocaleString() {
  return $toLocaleString.apply(TO_LOCALE_STRING_BUG ? $slice.call(aTypedArray(this)) : aTypedArray(this), arguments);
}, FORCED);


/***/ }),
/* 85 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var exportTypedArrayMethod = __webpack_require__(5).exportTypedArrayMethod;
var fails = __webpack_require__(4);
var global = __webpack_require__(3);

var Uint8Array = global.Uint8Array;
var Uint8ArrayPrototype = Uint8Array && Uint8Array.prototype || {};
var arrayToString = [].toString;
var arrayJoin = [].join;

if (fails(function () { arrayToString.call({}); })) {
  arrayToString = function toString() {
    return arrayJoin.call(this);
  };
}

var IS_NOT_ARRAY_METHOD = Uint8ArrayPrototype.toString != arrayToString;

// `%TypedArray%.prototype.toString` method
// https://tc39.github.io/ecma262/#sec-%typedarray%.prototype.tostring
exportTypedArrayMethod('toString', arrayToString, IS_NOT_ARRAY_METHOD);


/***/ }),
/* 86 */
/***/ (function(module, exports) {

if (typeof Object.create === 'function') {
  // implementation from standard node.js 'util' module
  module.exports = function inherits(ctor, superCtor) {
    if (superCtor) {
      ctor.super_ = superCtor
      ctor.prototype = Object.create(superCtor.prototype, {
        constructor: {
          value: ctor,
          enumerable: false,
          writable: true,
          configurable: true
        }
      })
    }
  };
} else {
  // old school shim for old browsers
  module.exports = function inherits(ctor, superCtor) {
    if (superCtor) {
      ctor.super_ = superCtor
      var TempCtor = function () {}
      TempCtor.prototype = superCtor.prototype
      ctor.prototype = new TempCtor()
      ctor.prototype.constructor = ctor
    }
  }
}


/***/ }),
/* 87 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var WordArray = C_lib.WordArray;
	    var C_enc = C.enc;

	    /**
	     * Base64 encoding strategy.
	     */
	    var Base64 = C_enc.Base64 = {
	        /**
	         * Converts a word array to a Base64 string.
	         *
	         * @param {WordArray} wordArray The word array.
	         *
	         * @return {string} The Base64 string.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var base64String = CryptoJS.enc.Base64.stringify(wordArray);
	         */
	        stringify: function (wordArray) {
	            // Shortcuts
	            var words = wordArray.words;
	            var sigBytes = wordArray.sigBytes;
	            var map = this._map;

	            // Clamp excess bits
	            wordArray.clamp();

	            // Convert
	            var base64Chars = [];
	            for (var i = 0; i < sigBytes; i += 3) {
	                var byte1 = (words[i >>> 2]       >>> (24 - (i % 4) * 8))       & 0xff;
	                var byte2 = (words[(i + 1) >>> 2] >>> (24 - ((i + 1) % 4) * 8)) & 0xff;
	                var byte3 = (words[(i + 2) >>> 2] >>> (24 - ((i + 2) % 4) * 8)) & 0xff;

	                var triplet = (byte1 << 16) | (byte2 << 8) | byte3;

	                for (var j = 0; (j < 4) && (i + j * 0.75 < sigBytes); j++) {
	                    base64Chars.push(map.charAt((triplet >>> (6 * (3 - j))) & 0x3f));
	                }
	            }

	            // Add padding
	            var paddingChar = map.charAt(64);
	            if (paddingChar) {
	                while (base64Chars.length % 4) {
	                    base64Chars.push(paddingChar);
	                }
	            }

	            return base64Chars.join('');
	        },

	        /**
	         * Converts a Base64 string to a word array.
	         *
	         * @param {string} base64Str The Base64 string.
	         *
	         * @return {WordArray} The word array.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var wordArray = CryptoJS.enc.Base64.parse(base64String);
	         */
	        parse: function (base64Str) {
	            // Shortcuts
	            var base64StrLength = base64Str.length;
	            var map = this._map;
	            var reverseMap = this._reverseMap;

	            if (!reverseMap) {
	                    reverseMap = this._reverseMap = [];
	                    for (var j = 0; j < map.length; j++) {
	                        reverseMap[map.charCodeAt(j)] = j;
	                    }
	            }

	            // Ignore padding
	            var paddingChar = map.charAt(64);
	            if (paddingChar) {
	                var paddingIndex = base64Str.indexOf(paddingChar);
	                if (paddingIndex !== -1) {
	                    base64StrLength = paddingIndex;
	                }
	            }

	            // Convert
	            return parseLoop(base64Str, base64StrLength, reverseMap);

	        },

	        _map: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='
	    };

	    function parseLoop(base64Str, base64StrLength, reverseMap) {
	      var words = [];
	      var nBytes = 0;
	      for (var i = 0; i < base64StrLength; i++) {
	          if (i % 4) {
	              var bits1 = reverseMap[base64Str.charCodeAt(i - 1)] << ((i % 4) * 2);
	              var bits2 = reverseMap[base64Str.charCodeAt(i)] >>> (6 - (i % 4) * 2);
	              words[nBytes >>> 2] |= (bits1 | bits2) << (24 - (nBytes % 4) * 8);
	              nBytes++;
	          }
	      }
	      return WordArray.create(words, nBytes);
	    }
	}());


	return CryptoJS.enc.Base64;

}));

/***/ }),
/* 88 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2));
	}
	else {}
}(this, function (CryptoJS) {

	(function (Math) {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var WordArray = C_lib.WordArray;
	    var Hasher = C_lib.Hasher;
	    var C_algo = C.algo;

	    // Constants table
	    var T = [];

	    // Compute constants
	    (function () {
	        for (var i = 0; i < 64; i++) {
	            T[i] = (Math.abs(Math.sin(i + 1)) * 0x100000000) | 0;
	        }
	    }());

	    /**
	     * MD5 hash algorithm.
	     */
	    var MD5 = C_algo.MD5 = Hasher.extend({
	        _doReset: function () {
	            this._hash = new WordArray.init([
	                0x67452301, 0xefcdab89,
	                0x98badcfe, 0x10325476
	            ]);
	        },

	        _doProcessBlock: function (M, offset) {
	            // Swap endian
	            for (var i = 0; i < 16; i++) {
	                // Shortcuts
	                var offset_i = offset + i;
	                var M_offset_i = M[offset_i];

	                M[offset_i] = (
	                    (((M_offset_i << 8)  | (M_offset_i >>> 24)) & 0x00ff00ff) |
	                    (((M_offset_i << 24) | (M_offset_i >>> 8))  & 0xff00ff00)
	                );
	            }

	            // Shortcuts
	            var H = this._hash.words;

	            var M_offset_0  = M[offset + 0];
	            var M_offset_1  = M[offset + 1];
	            var M_offset_2  = M[offset + 2];
	            var M_offset_3  = M[offset + 3];
	            var M_offset_4  = M[offset + 4];
	            var M_offset_5  = M[offset + 5];
	            var M_offset_6  = M[offset + 6];
	            var M_offset_7  = M[offset + 7];
	            var M_offset_8  = M[offset + 8];
	            var M_offset_9  = M[offset + 9];
	            var M_offset_10 = M[offset + 10];
	            var M_offset_11 = M[offset + 11];
	            var M_offset_12 = M[offset + 12];
	            var M_offset_13 = M[offset + 13];
	            var M_offset_14 = M[offset + 14];
	            var M_offset_15 = M[offset + 15];

	            // Working varialbes
	            var a = H[0];
	            var b = H[1];
	            var c = H[2];
	            var d = H[3];

	            // Computation
	            a = FF(a, b, c, d, M_offset_0,  7,  T[0]);
	            d = FF(d, a, b, c, M_offset_1,  12, T[1]);
	            c = FF(c, d, a, b, M_offset_2,  17, T[2]);
	            b = FF(b, c, d, a, M_offset_3,  22, T[3]);
	            a = FF(a, b, c, d, M_offset_4,  7,  T[4]);
	            d = FF(d, a, b, c, M_offset_5,  12, T[5]);
	            c = FF(c, d, a, b, M_offset_6,  17, T[6]);
	            b = FF(b, c, d, a, M_offset_7,  22, T[7]);
	            a = FF(a, b, c, d, M_offset_8,  7,  T[8]);
	            d = FF(d, a, b, c, M_offset_9,  12, T[9]);
	            c = FF(c, d, a, b, M_offset_10, 17, T[10]);
	            b = FF(b, c, d, a, M_offset_11, 22, T[11]);
	            a = FF(a, b, c, d, M_offset_12, 7,  T[12]);
	            d = FF(d, a, b, c, M_offset_13, 12, T[13]);
	            c = FF(c, d, a, b, M_offset_14, 17, T[14]);
	            b = FF(b, c, d, a, M_offset_15, 22, T[15]);

	            a = GG(a, b, c, d, M_offset_1,  5,  T[16]);
	            d = GG(d, a, b, c, M_offset_6,  9,  T[17]);
	            c = GG(c, d, a, b, M_offset_11, 14, T[18]);
	            b = GG(b, c, d, a, M_offset_0,  20, T[19]);
	            a = GG(a, b, c, d, M_offset_5,  5,  T[20]);
	            d = GG(d, a, b, c, M_offset_10, 9,  T[21]);
	            c = GG(c, d, a, b, M_offset_15, 14, T[22]);
	            b = GG(b, c, d, a, M_offset_4,  20, T[23]);
	            a = GG(a, b, c, d, M_offset_9,  5,  T[24]);
	            d = GG(d, a, b, c, M_offset_14, 9,  T[25]);
	            c = GG(c, d, a, b, M_offset_3,  14, T[26]);
	            b = GG(b, c, d, a, M_offset_8,  20, T[27]);
	            a = GG(a, b, c, d, M_offset_13, 5,  T[28]);
	            d = GG(d, a, b, c, M_offset_2,  9,  T[29]);
	            c = GG(c, d, a, b, M_offset_7,  14, T[30]);
	            b = GG(b, c, d, a, M_offset_12, 20, T[31]);

	            a = HH(a, b, c, d, M_offset_5,  4,  T[32]);
	            d = HH(d, a, b, c, M_offset_8,  11, T[33]);
	            c = HH(c, d, a, b, M_offset_11, 16, T[34]);
	            b = HH(b, c, d, a, M_offset_14, 23, T[35]);
	            a = HH(a, b, c, d, M_offset_1,  4,  T[36]);
	            d = HH(d, a, b, c, M_offset_4,  11, T[37]);
	            c = HH(c, d, a, b, M_offset_7,  16, T[38]);
	            b = HH(b, c, d, a, M_offset_10, 23, T[39]);
	            a = HH(a, b, c, d, M_offset_13, 4,  T[40]);
	            d = HH(d, a, b, c, M_offset_0,  11, T[41]);
	            c = HH(c, d, a, b, M_offset_3,  16, T[42]);
	            b = HH(b, c, d, a, M_offset_6,  23, T[43]);
	            a = HH(a, b, c, d, M_offset_9,  4,  T[44]);
	            d = HH(d, a, b, c, M_offset_12, 11, T[45]);
	            c = HH(c, d, a, b, M_offset_15, 16, T[46]);
	            b = HH(b, c, d, a, M_offset_2,  23, T[47]);

	            a = II(a, b, c, d, M_offset_0,  6,  T[48]);
	            d = II(d, a, b, c, M_offset_7,  10, T[49]);
	            c = II(c, d, a, b, M_offset_14, 15, T[50]);
	            b = II(b, c, d, a, M_offset_5,  21, T[51]);
	            a = II(a, b, c, d, M_offset_12, 6,  T[52]);
	            d = II(d, a, b, c, M_offset_3,  10, T[53]);
	            c = II(c, d, a, b, M_offset_10, 15, T[54]);
	            b = II(b, c, d, a, M_offset_1,  21, T[55]);
	            a = II(a, b, c, d, M_offset_8,  6,  T[56]);
	            d = II(d, a, b, c, M_offset_15, 10, T[57]);
	            c = II(c, d, a, b, M_offset_6,  15, T[58]);
	            b = II(b, c, d, a, M_offset_13, 21, T[59]);
	            a = II(a, b, c, d, M_offset_4,  6,  T[60]);
	            d = II(d, a, b, c, M_offset_11, 10, T[61]);
	            c = II(c, d, a, b, M_offset_2,  15, T[62]);
	            b = II(b, c, d, a, M_offset_9,  21, T[63]);

	            // Intermediate hash value
	            H[0] = (H[0] + a) | 0;
	            H[1] = (H[1] + b) | 0;
	            H[2] = (H[2] + c) | 0;
	            H[3] = (H[3] + d) | 0;
	        },

	        _doFinalize: function () {
	            // Shortcuts
	            var data = this._data;
	            var dataWords = data.words;

	            var nBitsTotal = this._nDataBytes * 8;
	            var nBitsLeft = data.sigBytes * 8;

	            // Add padding
	            dataWords[nBitsLeft >>> 5] |= 0x80 << (24 - nBitsLeft % 32);

	            var nBitsTotalH = Math.floor(nBitsTotal / 0x100000000);
	            var nBitsTotalL = nBitsTotal;
	            dataWords[(((nBitsLeft + 64) >>> 9) << 4) + 15] = (
	                (((nBitsTotalH << 8)  | (nBitsTotalH >>> 24)) & 0x00ff00ff) |
	                (((nBitsTotalH << 24) | (nBitsTotalH >>> 8))  & 0xff00ff00)
	            );
	            dataWords[(((nBitsLeft + 64) >>> 9) << 4) + 14] = (
	                (((nBitsTotalL << 8)  | (nBitsTotalL >>> 24)) & 0x00ff00ff) |
	                (((nBitsTotalL << 24) | (nBitsTotalL >>> 8))  & 0xff00ff00)
	            );

	            data.sigBytes = (dataWords.length + 1) * 4;

	            // Hash final blocks
	            this._process();

	            // Shortcuts
	            var hash = this._hash;
	            var H = hash.words;

	            // Swap endian
	            for (var i = 0; i < 4; i++) {
	                // Shortcut
	                var H_i = H[i];

	                H[i] = (((H_i << 8)  | (H_i >>> 24)) & 0x00ff00ff) |
	                       (((H_i << 24) | (H_i >>> 8))  & 0xff00ff00);
	            }

	            // Return final computed hash
	            return hash;
	        },

	        clone: function () {
	            var clone = Hasher.clone.call(this);
	            clone._hash = this._hash.clone();

	            return clone;
	        }
	    });

	    function FF(a, b, c, d, x, s, t) {
	        var n = a + ((b & c) | (~b & d)) + x + t;
	        return ((n << s) | (n >>> (32 - s))) + b;
	    }

	    function GG(a, b, c, d, x, s, t) {
	        var n = a + ((b & d) | (c & ~d)) + x + t;
	        return ((n << s) | (n >>> (32 - s))) + b;
	    }

	    function HH(a, b, c, d, x, s, t) {
	        var n = a + (b ^ c ^ d) + x + t;
	        return ((n << s) | (n >>> (32 - s))) + b;
	    }

	    function II(a, b, c, d, x, s, t) {
	        var n = a + (c ^ (b | ~d)) + x + t;
	        return ((n << s) | (n >>> (32 - s))) + b;
	    }

	    /**
	     * Shortcut function to the hasher's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     *
	     * @return {WordArray} The hash.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hash = CryptoJS.MD5('message');
	     *     var hash = CryptoJS.MD5(wordArray);
	     */
	    C.MD5 = Hasher._createHelper(MD5);

	    /**
	     * Shortcut function to the HMAC's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     * @param {WordArray|string} key The secret key.
	     *
	     * @return {WordArray} The HMAC.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hmac = CryptoJS.HmacMD5(message, key);
	     */
	    C.HmacMD5 = Hasher._createHmacHelper(MD5);
	}(Math));


	return CryptoJS.MD5;

}));

/***/ }),
/* 89 */
/***/ (function(module, exports, __webpack_require__) {

// Generated by CoffeeScript 1.7.1
(function() {
  var DecodeStream, Fixed, NumberT,
    __hasProp = {}.hasOwnProperty,
    __extends = function(child, parent) { for (var key in parent) { if (__hasProp.call(parent, key)) child[key] = parent[key]; } function ctor() { this.constructor = child; } ctor.prototype = parent.prototype; child.prototype = new ctor(); child.__super__ = parent.prototype; return child; };

  DecodeStream = __webpack_require__(169);

  NumberT = (function() {
    function NumberT(type, endian) {
      this.type = type;
      this.endian = endian != null ? endian : 'BE';
      this.fn = this.type;
      if (this.type[this.type.length - 1] !== '8') {
        this.fn += this.endian;
      }
    }

    NumberT.prototype.size = function() {
      return DecodeStream.TYPES[this.type];
    };

    NumberT.prototype.decode = function(stream) {
      return stream['read' + this.fn]();
    };

    NumberT.prototype.encode = function(stream, val) {
      return stream['write' + this.fn](val);
    };

    return NumberT;

  })();

  exports.Number = NumberT;

  exports.uint8 = new NumberT('UInt8');

  exports.uint16be = exports.uint16 = new NumberT('UInt16', 'BE');

  exports.uint16le = new NumberT('UInt16', 'LE');

  exports.uint24be = exports.uint24 = new NumberT('UInt24', 'BE');

  exports.uint24le = new NumberT('UInt24', 'LE');

  exports.uint32be = exports.uint32 = new NumberT('UInt32', 'BE');

  exports.uint32le = new NumberT('UInt32', 'LE');

  exports.int8 = new NumberT('Int8');

  exports.int16be = exports.int16 = new NumberT('Int16', 'BE');

  exports.int16le = new NumberT('Int16', 'LE');

  exports.int24be = exports.int24 = new NumberT('Int24', 'BE');

  exports.int24le = new NumberT('Int24', 'LE');

  exports.int32be = exports.int32 = new NumberT('Int32', 'BE');

  exports.int32le = new NumberT('Int32', 'LE');

  exports.floatbe = exports.float = new NumberT('Float', 'BE');

  exports.floatle = new NumberT('Float', 'LE');

  exports.doublebe = exports.double = new NumberT('Double', 'BE');

  exports.doublele = new NumberT('Double', 'LE');

  Fixed = (function(_super) {
    __extends(Fixed, _super);

    function Fixed(size, endian, fracBits) {
      if (fracBits == null) {
        fracBits = size >> 1;
      }
      Fixed.__super__.constructor.call(this, "Int" + size, endian);
      this._point = 1 << fracBits;
    }

    Fixed.prototype.decode = function(stream) {
      return Fixed.__super__.decode.call(this, stream) / this._point;
    };

    Fixed.prototype.encode = function(stream, val) {
      return Fixed.__super__.encode.call(this, stream, val * this._point | 0);
    };

    return Fixed;

  })(NumberT);

  exports.Fixed = Fixed;

  exports.fixed16be = exports.fixed16 = new Fixed(16, 'BE');

  exports.fixed16le = new Fixed(16, 'LE');

  exports.fixed32be = exports.fixed32 = new Fixed(32, 'BE');

  exports.fixed32le = new Fixed(32, 'LE');

}).call(this);


/***/ }),
/* 90 */
/***/ (function(module, exports, __webpack_require__) {

// 7.1.13 ToObject(argument)
var defined = __webpack_require__(173);
module.exports = function (it) {
  return Object(defined(it));
};


/***/ }),
/* 91 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $at = __webpack_require__(427)(true);

// 21.1.3.27 String.prototype[@@iterator]()
__webpack_require__(178)(String, 'String', function (iterated) {
  this._t = String(iterated); // target
  this._i = 0;                // next index
// 21.1.5.2.1 %StringIteratorPrototype%.next()
}, function () {
  var O = this._t;
  var index = this._i;
  var point;
  if (index >= O.length) return { value: undefined, done: true };
  point = $at(O, index);
  this._i += point.length;
  return { value: point, done: false };
});


/***/ }),
/* 92 */
/***/ (function(module, exports, __webpack_require__) {

var fails = __webpack_require__(4);
var classof = __webpack_require__(30);

var split = ''.split;

// fallback for non-array-like ES3 and non-enumerable old V8 strings
module.exports = fails(function () {
  // throws an error in rhino, see https://github.com/mozilla/rhino/issues/346
  // eslint-disable-next-line no-prototype-builtins
  return !Object('z').propertyIsEnumerable(0);
}) ? function (it) {
  return classof(it) == 'String' ? split.call(it, '') : Object(it);
} : Object;


/***/ }),
/* 93 */
/***/ (function(module, exports, __webpack_require__) {

var toIndexedObject = __webpack_require__(27);
var toLength = __webpack_require__(8);
var toAbsoluteIndex = __webpack_require__(43);

// `Array.prototype.{ indexOf, includes }` methods implementation
var createMethod = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIndexedObject($this);
    var length = toLength(O.length);
    var index = toAbsoluteIndex(fromIndex, length);
    var value;
    // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare
    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++];
      // eslint-disable-next-line no-self-compare
      if (value != value) return true;
    // Array#indexOf ignores holes, Array#includes - not
    } else for (;length > index; index++) {
      if ((IS_INCLUDES || index in O) && O[index] === el) return IS_INCLUDES || index || 0;
    } return !IS_INCLUDES && -1;
  };
};

module.exports = {
  // `Array.prototype.includes` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.includes
  includes: createMethod(true),
  // `Array.prototype.indexOf` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.indexof
  indexOf: createMethod(false)
};


/***/ }),
/* 94 */
/***/ (function(module, exports, __webpack_require__) {

var internalObjectKeys = __webpack_require__(201);
var enumBugKeys = __webpack_require__(142);

// `Object.keys` method
// https://tc39.github.io/ecma262/#sec-object.keys
module.exports = Object.keys || function keys(O) {
  return internalObjectKeys(O, enumBugKeys);
};


/***/ }),
/* 95 */
/***/ (function(module, exports, __webpack_require__) {

var defineProperty = __webpack_require__(15).f;
var has = __webpack_require__(14);
var wellKnownSymbol = __webpack_require__(6);

var TO_STRING_TAG = wellKnownSymbol('toStringTag');

module.exports = function (it, TAG, STATIC) {
  if (it && !has(it = STATIC ? it : it.prototype, TO_STRING_TAG)) {
    defineProperty(it, TO_STRING_TAG, { configurable: true, value: TAG });
  }
};


/***/ }),
/* 96 */
/***/ (function(module, exports, __webpack_require__) {

var aFunction = __webpack_require__(44);

// optional / simple context binding
module.exports = function (fn, that, length) {
  aFunction(fn);
  if (that === undefined) return fn;
  switch (length) {
    case 0: return function () {
      return fn.call(that);
    };
    case 1: return function (a) {
      return fn.call(that, a);
    };
    case 2: return function (a, b) {
      return fn.call(that, a, b);
    };
    case 3: return function (a, b, c) {
      return fn.call(that, a, b, c);
    };
  }
  return function (/* ...args */) {
    return fn.apply(that, arguments);
  };
};


/***/ }),
/* 97 */
/***/ (function(module, exports) {

module.exports = {};


/***/ }),
/* 98 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(Buffer) {// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

// NOTE: These type checking functions intentionally don't use `instanceof`
// because it is fragile and can be easily faked with `Object.create()`.

function isArray(arg) {
  if (Array.isArray) {
    return Array.isArray(arg);
  }
  return objectToString(arg) === '[object Array]';
}
exports.isArray = isArray;

function isBoolean(arg) {
  return typeof arg === 'boolean';
}
exports.isBoolean = isBoolean;

function isNull(arg) {
  return arg === null;
}
exports.isNull = isNull;

function isNullOrUndefined(arg) {
  return arg == null;
}
exports.isNullOrUndefined = isNullOrUndefined;

function isNumber(arg) {
  return typeof arg === 'number';
}
exports.isNumber = isNumber;

function isString(arg) {
  return typeof arg === 'string';
}
exports.isString = isString;

function isSymbol(arg) {
  return typeof arg === 'symbol';
}
exports.isSymbol = isSymbol;

function isUndefined(arg) {
  return arg === void 0;
}
exports.isUndefined = isUndefined;

function isRegExp(re) {
  return objectToString(re) === '[object RegExp]';
}
exports.isRegExp = isRegExp;

function isObject(arg) {
  return typeof arg === 'object' && arg !== null;
}
exports.isObject = isObject;

function isDate(d) {
  return objectToString(d) === '[object Date]';
}
exports.isDate = isDate;

function isError(e) {
  return (objectToString(e) === '[object Error]' || e instanceof Error);
}
exports.isError = isError;

function isFunction(arg) {
  return typeof arg === 'function';
}
exports.isFunction = isFunction;

function isPrimitive(arg) {
  return arg === null ||
         typeof arg === 'boolean' ||
         typeof arg === 'number' ||
         typeof arg === 'string' ||
         typeof arg === 'symbol' ||  // ES6 symbol
         typeof arg === 'undefined';
}
exports.isPrimitive = isPrimitive;

exports.isBuffer = Buffer.isBuffer;

function objectToString(o) {
  return Object.prototype.toString.call(o);
}

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer))

/***/ }),
/* 99 */
/***/ (function(module, exports) {

var toString = {}.toString;

module.exports = function (it) {
  return toString.call(it).slice(8, -1);
};


/***/ }),
/* 100 */
/***/ (function(module, exports) {

module.exports = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};


/***/ }),
/* 101 */
/***/ (function(module, exports) {

module.exports = function (it) {
  if (typeof it != 'function') throw TypeError(it + ' is not a function!');
  return it;
};


/***/ }),
/* 102 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(422);
var global = __webpack_require__(17);
var hide = __webpack_require__(41);
var Iterators = __webpack_require__(103);
var TO_STRING_TAG = __webpack_require__(21)('toStringTag');

var DOMIterables = ('CSSRuleList,CSSStyleDeclaration,CSSValueList,ClientRectList,DOMRectList,DOMStringList,' +
  'DOMTokenList,DataTransferItemList,FileList,HTMLAllCollection,HTMLCollection,HTMLFormElement,HTMLSelectElement,' +
  'MediaList,MimeTypeArray,NamedNodeMap,NodeList,PaintRequestList,Plugin,PluginArray,SVGLengthList,SVGNumberList,' +
  'SVGPathSegList,SVGPointList,SVGStringList,SVGTransformList,SourceBufferList,StyleSheetList,TextTrackCueList,' +
  'TextTrackList,TouchList').split(',');

for (var i = 0; i < DOMIterables.length; i++) {
  var NAME = DOMIterables[i];
  var Collection = global[NAME];
  var proto = Collection && Collection.prototype;
  if (proto && !proto[TO_STRING_TAG]) hide(proto, TO_STRING_TAG, NAME);
  Iterators[NAME] = Iterators.Array;
}


/***/ }),
/* 103 */
/***/ (function(module, exports) {

module.exports = {};


/***/ }),
/* 104 */
/***/ (function(module, exports) {

module.exports = true;


/***/ }),
/* 105 */
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.14 / 15.2.3.14 Object.keys(O)
var $keys = __webpack_require__(258);
var enumBugKeys = __webpack_require__(182);

module.exports = Object.keys || function keys(O) {
  return $keys(O, enumBugKeys);
};


/***/ }),
/* 106 */
/***/ (function(module, exports, __webpack_require__) {

var def = __webpack_require__(26).f;
var has = __webpack_require__(51);
var TAG = __webpack_require__(21)('toStringTag');

module.exports = function (it, tag, stat) {
  if (it && !has(it = stat ? it : it.prototype, TAG)) def(it, TAG, { configurable: true, value: tag });
};


/***/ }),
/* 107 */
/***/ (function(module, exports, __webpack_require__) {

var ctx = __webpack_require__(33);
var call = __webpack_require__(265);
var isArrayIter = __webpack_require__(266);
var anObject = __webpack_require__(29);
var toLength = __webpack_require__(130);
var getIterFn = __webpack_require__(183);
var BREAK = {};
var RETURN = {};
var exports = module.exports = function (iterable, entries, fn, that, ITERATOR) {
  var iterFn = ITERATOR ? function () { return iterable; } : getIterFn(iterable);
  var f = ctx(fn, that, entries ? 2 : 1);
  var index = 0;
  var length, step, iterator, result;
  if (typeof iterFn != 'function') throw TypeError(iterable + ' is not iterable!');
  // fast case for arrays with default iterator
  if (isArrayIter(iterFn)) for (length = toLength(iterable.length); length > index; index++) {
    result = entries ? f(anObject(step = iterable[index])[0], step[1]) : f(iterable[index]);
    if (result === BREAK || result === RETURN) return result;
  } else for (iterator = iterFn.call(iterable); !(step = iterator.next()).done;) {
    result = call(iterator, f, step.value, entries);
    if (result === BREAK || result === RETURN) return result;
  }
};
exports.BREAK = BREAK;
exports.RETURN = RETURN;


/***/ }),
/* 108 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var keys = __webpack_require__(272);
var hasSymbols = typeof Symbol === 'function' && typeof Symbol('foo') === 'symbol';

var toStr = Object.prototype.toString;
var concat = Array.prototype.concat;
var origDefineProperty = Object.defineProperty;

var isFunction = function (fn) {
	return typeof fn === 'function' && toStr.call(fn) === '[object Function]';
};

var arePropertyDescriptorsSupported = function () {
	var obj = {};
	try {
		origDefineProperty(obj, 'x', { enumerable: false, value: obj });
		// eslint-disable-next-line no-unused-vars, no-restricted-syntax
		for (var _ in obj) { // jscs:ignore disallowUnusedVariables
			return false;
		}
		return obj.x === obj;
	} catch (e) { /* this is IE 8. */
		return false;
	}
};
var supportsDescriptors = origDefineProperty && arePropertyDescriptorsSupported();

var defineProperty = function (object, name, value, predicate) {
	if (name in object && (!isFunction(predicate) || !predicate())) {
		return;
	}
	if (supportsDescriptors) {
		origDefineProperty(object, name, {
			configurable: true,
			enumerable: false,
			value: value,
			writable: true
		});
	} else {
		object[name] = value;
	}
};

var defineProperties = function (object, map) {
	var predicates = arguments.length > 2 ? arguments[2] : {};
	var props = keys(map);
	if (hasSymbols) {
		props = concat.call(props, Object.getOwnPropertySymbols(map));
	}
	for (var i = 0; i < props.length; i += 1) {
		defineProperty(object, props[i], map[props[i]], predicates[props[i]]);
	}
};

defineProperties.supportsDescriptors = !!supportsDescriptors;

module.exports = defineProperties;


/***/ }),
/* 109 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var aFunction = __webpack_require__(44);

var PromiseCapability = function (C) {
  var resolve, reject;
  this.promise = new C(function ($$resolve, $$reject) {
    if (resolve !== undefined || reject !== undefined) throw TypeError('Bad Promise constructor');
    resolve = $$resolve;
    reject = $$reject;
  });
  this.resolve = aFunction(resolve);
  this.reject = aFunction(reject);
};

// 25.4.1.5 NewPromiseCapability(C)
module.exports.f = function (C) {
  return new PromiseCapability(C);
};


/***/ }),
/* 110 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var nativePropertyIsEnumerable = {}.propertyIsEnumerable;
var getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;

// Nashorn ~ JDK8 bug
var NASHORN_BUG = getOwnPropertyDescriptor && !nativePropertyIsEnumerable.call({ 1: 2 }, 1);

// `Object.prototype.propertyIsEnumerable` method implementation
// https://tc39.github.io/ecma262/#sec-object.prototype.propertyisenumerable
exports.f = NASHORN_BUG ? function propertyIsEnumerable(V) {
  var descriptor = getOwnPropertyDescriptor(this, V);
  return !!descriptor && descriptor.enumerable;
} : nativePropertyIsEnumerable;


/***/ }),
/* 111 */
/***/ (function(module, exports, __webpack_require__) {

var shared = __webpack_require__(141);
var uid = __webpack_require__(112);

var keys = shared('keys');

module.exports = function (key) {
  return keys[key] || (keys[key] = uid(key));
};


/***/ }),
/* 112 */
/***/ (function(module, exports) {

var id = 0;
var postfix = Math.random();

module.exports = function (key) {
  return 'Symbol(' + String(key === undefined ? '' : key) + ')_' + (++id + postfix).toString(36);
};


/***/ }),
/* 113 */
/***/ (function(module, exports) {

module.exports = {};


/***/ }),
/* 114 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);

module.exports = global;


/***/ }),
/* 115 */
/***/ (function(module, exports, __webpack_require__) {

var classof = __webpack_require__(30);

// `IsArray` abstract operation
// https://tc39.github.io/ecma262/#sec-isarray
module.exports = Array.isArray || function isArray(arg) {
  return classof(arg) == 'Array';
};


/***/ }),
/* 116 */
/***/ (function(module, exports, __webpack_require__) {

var fails = __webpack_require__(4);
var wellKnownSymbol = __webpack_require__(6);
var V8_VERSION = __webpack_require__(147);

var SPECIES = wellKnownSymbol('species');

module.exports = function (METHOD_NAME) {
  // We can't use this feature detection in V8 since it causes
  // deoptimization and serious performance degradation
  // https://github.com/zloirock/core-js/issues/677
  return V8_VERSION >= 51 || !fails(function () {
    var array = [];
    var constructor = array.constructor = {};
    constructor[SPECIES] = function () {
      return { foo: 1 };
    };
    return array[METHOD_NAME](Boolean).foo !== 1;
  });
};


/***/ }),
/* 117 */
/***/ (function(module, exports, __webpack_require__) {

var TO_STRING_TAG_SUPPORT = __webpack_require__(152);
var classofRaw = __webpack_require__(30);
var wellKnownSymbol = __webpack_require__(6);

var TO_STRING_TAG = wellKnownSymbol('toStringTag');
// ES3 wrong here
var CORRECT_ARGUMENTS = classofRaw(function () { return arguments; }()) == 'Arguments';

// fallback for IE11 Script Access Denied error
var tryGet = function (it, key) {
  try {
    return it[key];
  } catch (error) { /* empty */ }
};

// getting tag from ES6+ `Object.prototype.toString`
module.exports = TO_STRING_TAG_SUPPORT ? classofRaw : function (it) {
  var O, tag, result;
  return it === undefined ? 'Undefined' : it === null ? 'Null'
    // @@toStringTag case
    : typeof (tag = tryGet(O = Object(it), TO_STRING_TAG)) == 'string' ? tag
    // builtinTag case
    : CORRECT_ARGUMENTS ? classofRaw(O)
    // ES3 arguments fallback
    : (result = classofRaw(O)) == 'Object' && typeof O.callee == 'function' ? 'Arguments' : result;
};


/***/ }),
/* 118 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var exec = __webpack_require__(119);

$({ target: 'RegExp', proto: true, forced: /./.exec !== exec }, {
  exec: exec
});


/***/ }),
/* 119 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var regexpFlags = __webpack_require__(228);
var stickyHelpers = __webpack_require__(329);

var nativeExec = RegExp.prototype.exec;
// This always refers to the native implementation, because the
// String#replace polyfill uses ./fix-regexp-well-known-symbol-logic.js,
// which loads this file before patching the method.
var nativeReplace = String.prototype.replace;

var patchedExec = nativeExec;

var UPDATES_LAST_INDEX_WRONG = (function () {
  var re1 = /a/;
  var re2 = /b*/g;
  nativeExec.call(re1, 'a');
  nativeExec.call(re2, 'a');
  return re1.lastIndex !== 0 || re2.lastIndex !== 0;
})();

var UNSUPPORTED_Y = stickyHelpers.UNSUPPORTED_Y || stickyHelpers.BROKEN_CARET;

// nonparticipating capturing group, copied from es5-shim's String#split patch.
var NPCG_INCLUDED = /()??/.exec('')[1] !== undefined;

var PATCH = UPDATES_LAST_INDEX_WRONG || NPCG_INCLUDED || UNSUPPORTED_Y;

if (PATCH) {
  patchedExec = function exec(str) {
    var re = this;
    var lastIndex, reCopy, match, i;
    var sticky = UNSUPPORTED_Y && re.sticky;
    var flags = regexpFlags.call(re);
    var source = re.source;
    var charsAdded = 0;
    var strCopy = str;

    if (sticky) {
      flags = flags.replace('y', '');
      if (flags.indexOf('g') === -1) {
        flags += 'g';
      }

      strCopy = String(str).slice(re.lastIndex);
      // Support anchored sticky behavior.
      if (re.lastIndex > 0 && (!re.multiline || re.multiline && str[re.lastIndex - 1] !== '\n')) {
        source = '(?: ' + source + ')';
        strCopy = ' ' + strCopy;
        charsAdded++;
      }
      // ^(? + rx + ) is needed, in combination with some str slicing, to
      // simulate the 'y' flag.
      reCopy = new RegExp('^(?:' + source + ')', flags);
    }

    if (NPCG_INCLUDED) {
      reCopy = new RegExp('^' + source + '$(?!\\s)', flags);
    }
    if (UPDATES_LAST_INDEX_WRONG) lastIndex = re.lastIndex;

    match = nativeExec.call(sticky ? reCopy : re, strCopy);

    if (sticky) {
      if (match) {
        match.input = match.input.slice(charsAdded);
        match[0] = match[0].slice(charsAdded);
        match.index = re.lastIndex;
        re.lastIndex += match[0].length;
      } else re.lastIndex = 0;
    } else if (UPDATES_LAST_INDEX_WRONG && match) {
      re.lastIndex = re.global ? match.index + match[0].length : lastIndex;
    }
    if (NPCG_INCLUDED && match && match.length > 1) {
      // Fix browsers whose `exec` methods don't consistently return `undefined`
      // for NPCG, like IE8. NOTE: This doesn' work for /(.?)?/
      nativeReplace.call(match[0], reCopy, function () {
        for (i = 1; i < arguments.length - 2; i++) {
          if (arguments[i] === undefined) match[i] = undefined;
        }
      });
    }

    return match;
  };
}

module.exports = patchedExec;


/***/ }),
/* 120 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var DOMIterables = __webpack_require__(239);
var ArrayIteratorMethods = __webpack_require__(28);
var createNonEnumerableProperty = __webpack_require__(18);
var wellKnownSymbol = __webpack_require__(6);

var ITERATOR = wellKnownSymbol('iterator');
var TO_STRING_TAG = wellKnownSymbol('toStringTag');
var ArrayValues = ArrayIteratorMethods.values;

for (var COLLECTION_NAME in DOMIterables) {
  var Collection = global[COLLECTION_NAME];
  var CollectionPrototype = Collection && Collection.prototype;
  if (CollectionPrototype) {
    // some Chrome versions have non-configurable methods on DOMTokenList
    if (CollectionPrototype[ITERATOR] !== ArrayValues) try {
      createNonEnumerableProperty(CollectionPrototype, ITERATOR, ArrayValues);
    } catch (error) {
      CollectionPrototype[ITERATOR] = ArrayValues;
    }
    if (!CollectionPrototype[TO_STRING_TAG]) {
      createNonEnumerableProperty(CollectionPrototype, TO_STRING_TAG, COLLECTION_NAME);
    }
    if (DOMIterables[COLLECTION_NAME]) for (var METHOD_NAME in ArrayIteratorMethods) {
      // some Chrome versions have non-configurable methods on DOMTokenList
      if (CollectionPrototype[METHOD_NAME] !== ArrayIteratorMethods[METHOD_NAME]) try {
        createNonEnumerableProperty(CollectionPrototype, METHOD_NAME, ArrayIteratorMethods[METHOD_NAME]);
      } catch (error) {
        CollectionPrototype[METHOD_NAME] = ArrayIteratorMethods[METHOD_NAME];
      }
    }
  }
}


/***/ }),
/* 121 */
/***/ (function(module, exports, __webpack_require__) {

// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

module.exports = Stream;

var EE = __webpack_require__(122).EventEmitter;
var inherits = __webpack_require__(86);

inherits(Stream, EE);
Stream.Readable = __webpack_require__(164);
Stream.Writable = __webpack_require__(346);
Stream.Duplex = __webpack_require__(347);
Stream.Transform = __webpack_require__(348);
Stream.PassThrough = __webpack_require__(349);

// Backwards-compat with node 0.4.x
Stream.Stream = Stream;



// old-style streams.  Note that the pipe method (the only relevant
// part of this class) is overridden in the Readable class.

function Stream() {
  EE.call(this);
}

Stream.prototype.pipe = function(dest, options) {
  var source = this;

  function ondata(chunk) {
    if (dest.writable) {
      if (false === dest.write(chunk) && source.pause) {
        source.pause();
      }
    }
  }

  source.on('data', ondata);

  function ondrain() {
    if (source.readable && source.resume) {
      source.resume();
    }
  }

  dest.on('drain', ondrain);

  // If the 'end' option is not supplied, dest.end() will be called when
  // source gets the 'end' or 'close' events.  Only dest.end() once.
  if (!dest._isStdio && (!options || options.end !== false)) {
    source.on('end', onend);
    source.on('close', onclose);
  }

  var didOnEnd = false;
  function onend() {
    if (didOnEnd) return;
    didOnEnd = true;

    dest.end();
  }


  function onclose() {
    if (didOnEnd) return;
    didOnEnd = true;

    if (typeof dest.destroy === 'function') dest.destroy();
  }

  // don't leave dangling pipes when there are errors.
  function onerror(er) {
    cleanup();
    if (EE.listenerCount(this, 'error') === 0) {
      throw er; // Unhandled stream error in pipe.
    }
  }

  source.on('error', onerror);
  dest.on('error', onerror);

  // remove all the event listeners that were added.
  function cleanup() {
    source.removeListener('data', ondata);
    dest.removeListener('drain', ondrain);

    source.removeListener('end', onend);
    source.removeListener('close', onclose);

    source.removeListener('error', onerror);
    dest.removeListener('error', onerror);

    source.removeListener('end', cleanup);
    source.removeListener('close', cleanup);

    dest.removeListener('close', cleanup);
  }

  source.on('end', cleanup);
  source.on('close', cleanup);

  dest.on('close', cleanup);

  dest.emit('pipe', source);

  // Allow for unix-like usage: A.pipe(B).pipe(C)
  return dest;
};


/***/ }),
/* 122 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.



var R = typeof Reflect === 'object' ? Reflect : null
var ReflectApply = R && typeof R.apply === 'function'
  ? R.apply
  : function ReflectApply(target, receiver, args) {
    return Function.prototype.apply.call(target, receiver, args);
  }

var ReflectOwnKeys
if (R && typeof R.ownKeys === 'function') {
  ReflectOwnKeys = R.ownKeys
} else if (Object.getOwnPropertySymbols) {
  ReflectOwnKeys = function ReflectOwnKeys(target) {
    return Object.getOwnPropertyNames(target)
      .concat(Object.getOwnPropertySymbols(target));
  };
} else {
  ReflectOwnKeys = function ReflectOwnKeys(target) {
    return Object.getOwnPropertyNames(target);
  };
}

function ProcessEmitWarning(warning) {
  if (console && console.warn) console.warn(warning);
}

var NumberIsNaN = Number.isNaN || function NumberIsNaN(value) {
  return value !== value;
}

function EventEmitter() {
  EventEmitter.init.call(this);
}
module.exports = EventEmitter;
module.exports.once = once;

// Backwards-compat with node 0.10.x
EventEmitter.EventEmitter = EventEmitter;

EventEmitter.prototype._events = undefined;
EventEmitter.prototype._eventsCount = 0;
EventEmitter.prototype._maxListeners = undefined;

// By default EventEmitters will print a warning if more than 10 listeners are
// added to it. This is a useful default which helps finding memory leaks.
var defaultMaxListeners = 10;

function checkListener(listener) {
  if (typeof listener !== 'function') {
    throw new TypeError('The "listener" argument must be of type Function. Received type ' + typeof listener);
  }
}

Object.defineProperty(EventEmitter, 'defaultMaxListeners', {
  enumerable: true,
  get: function() {
    return defaultMaxListeners;
  },
  set: function(arg) {
    if (typeof arg !== 'number' || arg < 0 || NumberIsNaN(arg)) {
      throw new RangeError('The value of "defaultMaxListeners" is out of range. It must be a non-negative number. Received ' + arg + '.');
    }
    defaultMaxListeners = arg;
  }
});

EventEmitter.init = function() {

  if (this._events === undefined ||
      this._events === Object.getPrototypeOf(this)._events) {
    this._events = Object.create(null);
    this._eventsCount = 0;
  }

  this._maxListeners = this._maxListeners || undefined;
};

// Obviously not all Emitters should be limited to 10. This function allows
// that to be increased. Set to zero for unlimited.
EventEmitter.prototype.setMaxListeners = function setMaxListeners(n) {
  if (typeof n !== 'number' || n < 0 || NumberIsNaN(n)) {
    throw new RangeError('The value of "n" is out of range. It must be a non-negative number. Received ' + n + '.');
  }
  this._maxListeners = n;
  return this;
};

function _getMaxListeners(that) {
  if (that._maxListeners === undefined)
    return EventEmitter.defaultMaxListeners;
  return that._maxListeners;
}

EventEmitter.prototype.getMaxListeners = function getMaxListeners() {
  return _getMaxListeners(this);
};

EventEmitter.prototype.emit = function emit(type) {
  var args = [];
  for (var i = 1; i < arguments.length; i++) args.push(arguments[i]);
  var doError = (type === 'error');

  var events = this._events;
  if (events !== undefined)
    doError = (doError && events.error === undefined);
  else if (!doError)
    return false;

  // If there is no 'error' event listener then throw.
  if (doError) {
    var er;
    if (args.length > 0)
      er = args[0];
    if (er instanceof Error) {
      // Note: The comments on the `throw` lines are intentional, they show
      // up in Node's output if this results in an unhandled exception.
      throw er; // Unhandled 'error' event
    }
    // At least give some kind of context to the user
    var err = new Error('Unhandled error.' + (er ? ' (' + er.message + ')' : ''));
    err.context = er;
    throw err; // Unhandled 'error' event
  }

  var handler = events[type];

  if (handler === undefined)
    return false;

  if (typeof handler === 'function') {
    ReflectApply(handler, this, args);
  } else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      ReflectApply(listeners[i], this, args);
  }

  return true;
};

function _addListener(target, type, listener, prepend) {
  var m;
  var events;
  var existing;

  checkListener(listener);

  events = target._events;
  if (events === undefined) {
    events = target._events = Object.create(null);
    target._eventsCount = 0;
  } else {
    // To avoid recursion in the case that type === "newListener"! Before
    // adding it to the listeners, first emit "newListener".
    if (events.newListener !== undefined) {
      target.emit('newListener', type,
                  listener.listener ? listener.listener : listener);

      // Re-assign `events` because a newListener handler could have caused the
      // this._events to be assigned to a new object
      events = target._events;
    }
    existing = events[type];
  }

  if (existing === undefined) {
    // Optimize the case of one listener. Don't need the extra array object.
    existing = events[type] = listener;
    ++target._eventsCount;
  } else {
    if (typeof existing === 'function') {
      // Adding the second element, need to change to array.
      existing = events[type] =
        prepend ? [listener, existing] : [existing, listener];
      // If we've already got an array, just append.
    } else if (prepend) {
      existing.unshift(listener);
    } else {
      existing.push(listener);
    }

    // Check for listener leak
    m = _getMaxListeners(target);
    if (m > 0 && existing.length > m && !existing.warned) {
      existing.warned = true;
      // No error code for this since it is a Warning
      // eslint-disable-next-line no-restricted-syntax
      var w = new Error('Possible EventEmitter memory leak detected. ' +
                          existing.length + ' ' + String(type) + ' listeners ' +
                          'added. Use emitter.setMaxListeners() to ' +
                          'increase limit');
      w.name = 'MaxListenersExceededWarning';
      w.emitter = target;
      w.type = type;
      w.count = existing.length;
      ProcessEmitWarning(w);
    }
  }

  return target;
}

EventEmitter.prototype.addListener = function addListener(type, listener) {
  return _addListener(this, type, listener, false);
};

EventEmitter.prototype.on = EventEmitter.prototype.addListener;

EventEmitter.prototype.prependListener =
    function prependListener(type, listener) {
      return _addListener(this, type, listener, true);
    };

function onceWrapper() {
  if (!this.fired) {
    this.target.removeListener(this.type, this.wrapFn);
    this.fired = true;
    if (arguments.length === 0)
      return this.listener.call(this.target);
    return this.listener.apply(this.target, arguments);
  }
}

function _onceWrap(target, type, listener) {
  var state = { fired: false, wrapFn: undefined, target: target, type: type, listener: listener };
  var wrapped = onceWrapper.bind(state);
  wrapped.listener = listener;
  state.wrapFn = wrapped;
  return wrapped;
}

EventEmitter.prototype.once = function once(type, listener) {
  checkListener(listener);
  this.on(type, _onceWrap(this, type, listener));
  return this;
};

EventEmitter.prototype.prependOnceListener =
    function prependOnceListener(type, listener) {
      checkListener(listener);
      this.prependListener(type, _onceWrap(this, type, listener));
      return this;
    };

// Emits a 'removeListener' event if and only if the listener was removed.
EventEmitter.prototype.removeListener =
    function removeListener(type, listener) {
      var list, events, position, i, originalListener;

      checkListener(listener);

      events = this._events;
      if (events === undefined)
        return this;

      list = events[type];
      if (list === undefined)
        return this;

      if (list === listener || list.listener === listener) {
        if (--this._eventsCount === 0)
          this._events = Object.create(null);
        else {
          delete events[type];
          if (events.removeListener)
            this.emit('removeListener', type, list.listener || listener);
        }
      } else if (typeof list !== 'function') {
        position = -1;

        for (i = list.length - 1; i >= 0; i--) {
          if (list[i] === listener || list[i].listener === listener) {
            originalListener = list[i].listener;
            position = i;
            break;
          }
        }

        if (position < 0)
          return this;

        if (position === 0)
          list.shift();
        else {
          spliceOne(list, position);
        }

        if (list.length === 1)
          events[type] = list[0];

        if (events.removeListener !== undefined)
          this.emit('removeListener', type, originalListener || listener);
      }

      return this;
    };

EventEmitter.prototype.off = EventEmitter.prototype.removeListener;

EventEmitter.prototype.removeAllListeners =
    function removeAllListeners(type) {
      var listeners, events, i;

      events = this._events;
      if (events === undefined)
        return this;

      // not listening for removeListener, no need to emit
      if (events.removeListener === undefined) {
        if (arguments.length === 0) {
          this._events = Object.create(null);
          this._eventsCount = 0;
        } else if (events[type] !== undefined) {
          if (--this._eventsCount === 0)
            this._events = Object.create(null);
          else
            delete events[type];
        }
        return this;
      }

      // emit removeListener for all listeners on all events
      if (arguments.length === 0) {
        var keys = Object.keys(events);
        var key;
        for (i = 0; i < keys.length; ++i) {
          key = keys[i];
          if (key === 'removeListener') continue;
          this.removeAllListeners(key);
        }
        this.removeAllListeners('removeListener');
        this._events = Object.create(null);
        this._eventsCount = 0;
        return this;
      }

      listeners = events[type];

      if (typeof listeners === 'function') {
        this.removeListener(type, listeners);
      } else if (listeners !== undefined) {
        // LIFO order
        for (i = listeners.length - 1; i >= 0; i--) {
          this.removeListener(type, listeners[i]);
        }
      }

      return this;
    };

function _listeners(target, type, unwrap) {
  var events = target._events;

  if (events === undefined)
    return [];

  var evlistener = events[type];
  if (evlistener === undefined)
    return [];

  if (typeof evlistener === 'function')
    return unwrap ? [evlistener.listener || evlistener] : [evlistener];

  return unwrap ?
    unwrapListeners(evlistener) : arrayClone(evlistener, evlistener.length);
}

EventEmitter.prototype.listeners = function listeners(type) {
  return _listeners(this, type, true);
};

EventEmitter.prototype.rawListeners = function rawListeners(type) {
  return _listeners(this, type, false);
};

EventEmitter.listenerCount = function(emitter, type) {
  if (typeof emitter.listenerCount === 'function') {
    return emitter.listenerCount(type);
  } else {
    return listenerCount.call(emitter, type);
  }
};

EventEmitter.prototype.listenerCount = listenerCount;
function listenerCount(type) {
  var events = this._events;

  if (events !== undefined) {
    var evlistener = events[type];

    if (typeof evlistener === 'function') {
      return 1;
    } else if (evlistener !== undefined) {
      return evlistener.length;
    }
  }

  return 0;
}

EventEmitter.prototype.eventNames = function eventNames() {
  return this._eventsCount > 0 ? ReflectOwnKeys(this._events) : [];
};

function arrayClone(arr, n) {
  var copy = new Array(n);
  for (var i = 0; i < n; ++i)
    copy[i] = arr[i];
  return copy;
}

function spliceOne(list, index) {
  for (; index + 1 < list.length; index++)
    list[index] = list[index + 1];
  list.pop();
}

function unwrapListeners(arr) {
  var ret = new Array(arr.length);
  for (var i = 0; i < ret.length; ++i) {
    ret[i] = arr[i].listener || arr[i];
  }
  return ret;
}

function once(emitter, name) {
  return new Promise(function (resolve, reject) {
    function eventListener() {
      if (errorListener !== undefined) {
        emitter.removeListener('error', errorListener);
      }
      resolve([].slice.call(arguments));
    };
    var errorListener;

    // Adding an error listener is not optional because
    // if an error is thrown on an event emitter we cannot
    // guarantee that the actual event we are waiting will
    // be fired. The result could be a silent way to create
    // memory or file descriptor leaks, which is something
    // we should avoid.
    if (name !== 'error') {
      errorListener = function errorListener(err) {
        emitter.removeListener(name, eventListener);
        reject(err);
      };

      emitter.once('error', errorListener);
    }

    emitter.once(name, eventListener);
  });
}


/***/ }),
/* 123 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(process) {

if (typeof process === 'undefined' ||
    !process.version ||
    process.version.indexOf('v0.') === 0 ||
    process.version.indexOf('v1.') === 0 && process.version.indexOf('v1.8.') !== 0) {
  module.exports = { nextTick: nextTick };
} else {
  module.exports = process
}

function nextTick(fn, arg1, arg2, arg3) {
  if (typeof fn !== 'function') {
    throw new TypeError('"callback" argument must be a function');
  }
  var len = arguments.length;
  var args, i;
  switch (len) {
  case 0:
  case 1:
    return process.nextTick(fn);
  case 2:
    return process.nextTick(function afterTickOne() {
      fn.call(null, arg1);
    });
  case 3:
    return process.nextTick(function afterTickTwo() {
      fn.call(null, arg1, arg2);
    });
  case 4:
    return process.nextTick(function afterTickThree() {
      fn.call(null, arg1, arg2, arg3);
    });
  default:
    args = new Array(len - 1);
    i = 0;
    while (i < args.length) {
      args[i++] = arguments[i];
    }
    return process.nextTick(function afterTick() {
      fn.apply(null, args);
    });
  }
}


/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(47)))

/***/ }),
/* 124 */
/***/ (function(module, exports, __webpack_require__) {

/* eslint-disable node/no-deprecated-api */
var buffer = __webpack_require__(10)
var Buffer = buffer.Buffer

// alternative to using Object.keys for old browsers
function copyProps (src, dst) {
  for (var key in src) {
    dst[key] = src[key]
  }
}
if (Buffer.from && Buffer.alloc && Buffer.allocUnsafe && Buffer.allocUnsafeSlow) {
  module.exports = buffer
} else {
  // Copy properties from require('buffer')
  copyProps(buffer, exports)
  exports.Buffer = SafeBuffer
}

function SafeBuffer (arg, encodingOrOffset, length) {
  return Buffer(arg, encodingOrOffset, length)
}

// Copy static methods from Buffer
copyProps(Buffer, SafeBuffer)

SafeBuffer.from = function (arg, encodingOrOffset, length) {
  if (typeof arg === 'number') {
    throw new TypeError('Argument must not be a number')
  }
  return Buffer(arg, encodingOrOffset, length)
}

SafeBuffer.alloc = function (size, fill, encoding) {
  if (typeof size !== 'number') {
    throw new TypeError('Argument must be a number')
  }
  var buf = Buffer(size)
  if (fill !== undefined) {
    if (typeof encoding === 'string') {
      buf.fill(fill, encoding)
    } else {
      buf.fill(fill)
    }
  } else {
    buf.fill(0)
  }
  return buf
}

SafeBuffer.allocUnsafe = function (size) {
  if (typeof size !== 'number') {
    throw new TypeError('Argument must be a number')
  }
  return Buffer(size)
}

SafeBuffer.allocUnsafeSlow = function (size) {
  if (typeof size !== 'number') {
    throw new TypeError('Argument must be a number')
  }
  return buffer.SlowBuffer(size)
}


/***/ }),
/* 125 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.



/*<replacement>*/

var Buffer = __webpack_require__(124).Buffer;
/*</replacement>*/

var isEncoding = Buffer.isEncoding || function (encoding) {
  encoding = '' + encoding;
  switch (encoding && encoding.toLowerCase()) {
    case 'hex':case 'utf8':case 'utf-8':case 'ascii':case 'binary':case 'base64':case 'ucs2':case 'ucs-2':case 'utf16le':case 'utf-16le':case 'raw':
      return true;
    default:
      return false;
  }
};

function _normalizeEncoding(enc) {
  if (!enc) return 'utf8';
  var retried;
  while (true) {
    switch (enc) {
      case 'utf8':
      case 'utf-8':
        return 'utf8';
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return 'utf16le';
      case 'latin1':
      case 'binary':
        return 'latin1';
      case 'base64':
      case 'ascii':
      case 'hex':
        return enc;
      default:
        if (retried) return; // undefined
        enc = ('' + enc).toLowerCase();
        retried = true;
    }
  }
};

// Do not cache `Buffer.isEncoding` when checking encoding names as some
// modules monkey-patch it to support additional encodings
function normalizeEncoding(enc) {
  var nenc = _normalizeEncoding(enc);
  if (typeof nenc !== 'string' && (Buffer.isEncoding === isEncoding || !isEncoding(enc))) throw new Error('Unknown encoding: ' + enc);
  return nenc || enc;
}

// StringDecoder provides an interface for efficiently splitting a series of
// buffers into a series of JS strings without breaking apart multi-byte
// characters.
exports.StringDecoder = StringDecoder;
function StringDecoder(encoding) {
  this.encoding = normalizeEncoding(encoding);
  var nb;
  switch (this.encoding) {
    case 'utf16le':
      this.text = utf16Text;
      this.end = utf16End;
      nb = 4;
      break;
    case 'utf8':
      this.fillLast = utf8FillLast;
      nb = 4;
      break;
    case 'base64':
      this.text = base64Text;
      this.end = base64End;
      nb = 3;
      break;
    default:
      this.write = simpleWrite;
      this.end = simpleEnd;
      return;
  }
  this.lastNeed = 0;
  this.lastTotal = 0;
  this.lastChar = Buffer.allocUnsafe(nb);
}

StringDecoder.prototype.write = function (buf) {
  if (buf.length === 0) return '';
  var r;
  var i;
  if (this.lastNeed) {
    r = this.fillLast(buf);
    if (r === undefined) return '';
    i = this.lastNeed;
    this.lastNeed = 0;
  } else {
    i = 0;
  }
  if (i < buf.length) return r ? r + this.text(buf, i) : this.text(buf, i);
  return r || '';
};

StringDecoder.prototype.end = utf8End;

// Returns only complete characters in a Buffer
StringDecoder.prototype.text = utf8Text;

// Attempts to complete a partial non-UTF-8 character using bytes from a Buffer
StringDecoder.prototype.fillLast = function (buf) {
  if (this.lastNeed <= buf.length) {
    buf.copy(this.lastChar, this.lastTotal - this.lastNeed, 0, this.lastNeed);
    return this.lastChar.toString(this.encoding, 0, this.lastTotal);
  }
  buf.copy(this.lastChar, this.lastTotal - this.lastNeed, 0, buf.length);
  this.lastNeed -= buf.length;
};

// Checks the type of a UTF-8 byte, whether it's ASCII, a leading byte, or a
// continuation byte. If an invalid byte is detected, -2 is returned.
function utf8CheckByte(byte) {
  if (byte <= 0x7F) return 0;else if (byte >> 5 === 0x06) return 2;else if (byte >> 4 === 0x0E) return 3;else if (byte >> 3 === 0x1E) return 4;
  return byte >> 6 === 0x02 ? -1 : -2;
}

// Checks at most 3 bytes at the end of a Buffer in order to detect an
// incomplete multi-byte UTF-8 character. The total number of bytes (2, 3, or 4)
// needed to complete the UTF-8 character (if applicable) are returned.
function utf8CheckIncomplete(self, buf, i) {
  var j = buf.length - 1;
  if (j < i) return 0;
  var nb = utf8CheckByte(buf[j]);
  if (nb >= 0) {
    if (nb > 0) self.lastNeed = nb - 1;
    return nb;
  }
  if (--j < i || nb === -2) return 0;
  nb = utf8CheckByte(buf[j]);
  if (nb >= 0) {
    if (nb > 0) self.lastNeed = nb - 2;
    return nb;
  }
  if (--j < i || nb === -2) return 0;
  nb = utf8CheckByte(buf[j]);
  if (nb >= 0) {
    if (nb > 0) {
      if (nb === 2) nb = 0;else self.lastNeed = nb - 3;
    }
    return nb;
  }
  return 0;
}

// Validates as many continuation bytes for a multi-byte UTF-8 character as
// needed or are available. If we see a non-continuation byte where we expect
// one, we "replace" the validated continuation bytes we've seen so far with
// a single UTF-8 replacement character ('\ufffd'), to match v8's UTF-8 decoding
// behavior. The continuation byte check is included three times in the case
// where all of the continuation bytes for a character exist in the same buffer.
// It is also done this way as a slight performance increase instead of using a
// loop.
function utf8CheckExtraBytes(self, buf, p) {
  if ((buf[0] & 0xC0) !== 0x80) {
    self.lastNeed = 0;
    return '\ufffd';
  }
  if (self.lastNeed > 1 && buf.length > 1) {
    if ((buf[1] & 0xC0) !== 0x80) {
      self.lastNeed = 1;
      return '\ufffd';
    }
    if (self.lastNeed > 2 && buf.length > 2) {
      if ((buf[2] & 0xC0) !== 0x80) {
        self.lastNeed = 2;
        return '\ufffd';
      }
    }
  }
}

// Attempts to complete a multi-byte UTF-8 character using bytes from a Buffer.
function utf8FillLast(buf) {
  var p = this.lastTotal - this.lastNeed;
  var r = utf8CheckExtraBytes(this, buf, p);
  if (r !== undefined) return r;
  if (this.lastNeed <= buf.length) {
    buf.copy(this.lastChar, p, 0, this.lastNeed);
    return this.lastChar.toString(this.encoding, 0, this.lastTotal);
  }
  buf.copy(this.lastChar, p, 0, buf.length);
  this.lastNeed -= buf.length;
}

// Returns all complete UTF-8 characters in a Buffer. If the Buffer ended on a
// partial character, the character's bytes are buffered until the required
// number of bytes are available.
function utf8Text(buf, i) {
  var total = utf8CheckIncomplete(this, buf, i);
  if (!this.lastNeed) return buf.toString('utf8', i);
  this.lastTotal = total;
  var end = buf.length - (total - this.lastNeed);
  buf.copy(this.lastChar, 0, end);
  return buf.toString('utf8', i, end);
}

// For UTF-8, a replacement character is added when ending on a partial
// character.
function utf8End(buf) {
  var r = buf && buf.length ? this.write(buf) : '';
  if (this.lastNeed) return r + '\ufffd';
  return r;
}

// UTF-16LE typically needs two bytes per character, but even if we have an even
// number of bytes available, we need to check if we end on a leading/high
// surrogate. In that case, we need to wait for the next two bytes in order to
// decode the last character properly.
function utf16Text(buf, i) {
  if ((buf.length - i) % 2 === 0) {
    var r = buf.toString('utf16le', i);
    if (r) {
      var c = r.charCodeAt(r.length - 1);
      if (c >= 0xD800 && c <= 0xDBFF) {
        this.lastNeed = 2;
        this.lastTotal = 4;
        this.lastChar[0] = buf[buf.length - 2];
        this.lastChar[1] = buf[buf.length - 1];
        return r.slice(0, -1);
      }
    }
    return r;
  }
  this.lastNeed = 1;
  this.lastTotal = 2;
  this.lastChar[0] = buf[buf.length - 1];
  return buf.toString('utf16le', i, buf.length - 1);
}

// For UTF-16LE we do not explicitly append special replacement characters if we
// end on a partial character, we simply let v8 handle that.
function utf16End(buf) {
  var r = buf && buf.length ? this.write(buf) : '';
  if (this.lastNeed) {
    var end = this.lastTotal - this.lastNeed;
    return r + this.lastChar.toString('utf16le', 0, end);
  }
  return r;
}

function base64Text(buf, i) {
  var n = (buf.length - i) % 3;
  if (n === 0) return buf.toString('base64', i);
  this.lastNeed = 3 - n;
  this.lastTotal = 3;
  if (n === 1) {
    this.lastChar[0] = buf[buf.length - 1];
  } else {
    this.lastChar[0] = buf[buf.length - 2];
    this.lastChar[1] = buf[buf.length - 1];
  }
  return buf.toString('base64', i, buf.length - n);
}

function base64End(buf) {
  var r = buf && buf.length ? this.write(buf) : '';
  if (this.lastNeed) return r + this.lastChar.toString('base64', 0, 3 - this.lastNeed);
  return r;
}

// Pass bytes on through for single-byte encodings (e.g. ascii, latin1, hex)
function simpleWrite(buf) {
  return buf.toString(this.encoding);
}

function simpleEnd(buf) {
  return buf && buf.length ? this.write(buf) : '';
}

/***/ }),
/* 126 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";



var TYPED_OK =  (typeof Uint8Array !== 'undefined') &&
                (typeof Uint16Array !== 'undefined') &&
                (typeof Int32Array !== 'undefined');

function _has(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

exports.assign = function (obj /*from1, from2, from3, ...*/) {
  var sources = Array.prototype.slice.call(arguments, 1);
  while (sources.length) {
    var source = sources.shift();
    if (!source) { continue; }

    if (typeof source !== 'object') {
      throw new TypeError(source + 'must be non-object');
    }

    for (var p in source) {
      if (_has(source, p)) {
        obj[p] = source[p];
      }
    }
  }

  return obj;
};


// reduce buffer size, avoiding mem copy
exports.shrinkBuf = function (buf, size) {
  if (buf.length === size) { return buf; }
  if (buf.subarray) { return buf.subarray(0, size); }
  buf.length = size;
  return buf;
};


var fnTyped = {
  arraySet: function (dest, src, src_offs, len, dest_offs) {
    if (src.subarray && dest.subarray) {
      dest.set(src.subarray(src_offs, src_offs + len), dest_offs);
      return;
    }
    // Fallback to ordinary array
    for (var i = 0; i < len; i++) {
      dest[dest_offs + i] = src[src_offs + i];
    }
  },
  // Join array of chunks to single array.
  flattenChunks: function (chunks) {
    var i, l, len, pos, chunk, result;

    // calculate data length
    len = 0;
    for (i = 0, l = chunks.length; i < l; i++) {
      len += chunks[i].length;
    }

    // join chunks
    result = new Uint8Array(len);
    pos = 0;
    for (i = 0, l = chunks.length; i < l; i++) {
      chunk = chunks[i];
      result.set(chunk, pos);
      pos += chunk.length;
    }

    return result;
  }
};

var fnUntyped = {
  arraySet: function (dest, src, src_offs, len, dest_offs) {
    for (var i = 0; i < len; i++) {
      dest[dest_offs + i] = src[src_offs + i];
    }
  },
  // Join array of chunks to single array.
  flattenChunks: function (chunks) {
    return [].concat.apply([], chunks);
  }
};


// Enable/Disable typed arrays use, for testing
//
exports.setTyped = function (on) {
  if (on) {
    exports.Buf8  = Uint8Array;
    exports.Buf16 = Uint16Array;
    exports.Buf32 = Int32Array;
    exports.assign(exports, fnTyped);
  } else {
    exports.Buf8  = Array;
    exports.Buf16 = Array;
    exports.Buf32 = Array;
    exports.assign(exports, fnUntyped);
  }
};

exports.setTyped(TYPED_OK);


/***/ }),
/* 127 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2));
	}
	else {}
}(this, function (CryptoJS) {

	(function (undefined) {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var Base = C_lib.Base;
	    var X32WordArray = C_lib.WordArray;

	    /**
	     * x64 namespace.
	     */
	    var C_x64 = C.x64 = {};

	    /**
	     * A 64-bit word.
	     */
	    var X64Word = C_x64.Word = Base.extend({
	        /**
	         * Initializes a newly created 64-bit word.
	         *
	         * @param {number} high The high 32 bits.
	         * @param {number} low The low 32 bits.
	         *
	         * @example
	         *
	         *     var x64Word = CryptoJS.x64.Word.create(0x00010203, 0x04050607);
	         */
	        init: function (high, low) {
	            this.high = high;
	            this.low = low;
	        }

	        /**
	         * Bitwise NOTs this word.
	         *
	         * @return {X64Word} A new x64-Word object after negating.
	         *
	         * @example
	         *
	         *     var negated = x64Word.not();
	         */
	        // not: function () {
	            // var high = ~this.high;
	            // var low = ~this.low;

	            // return X64Word.create(high, low);
	        // },

	        /**
	         * Bitwise ANDs this word with the passed word.
	         *
	         * @param {X64Word} word The x64-Word to AND with this word.
	         *
	         * @return {X64Word} A new x64-Word object after ANDing.
	         *
	         * @example
	         *
	         *     var anded = x64Word.and(anotherX64Word);
	         */
	        // and: function (word) {
	            // var high = this.high & word.high;
	            // var low = this.low & word.low;

	            // return X64Word.create(high, low);
	        // },

	        /**
	         * Bitwise ORs this word with the passed word.
	         *
	         * @param {X64Word} word The x64-Word to OR with this word.
	         *
	         * @return {X64Word} A new x64-Word object after ORing.
	         *
	         * @example
	         *
	         *     var ored = x64Word.or(anotherX64Word);
	         */
	        // or: function (word) {
	            // var high = this.high | word.high;
	            // var low = this.low | word.low;

	            // return X64Word.create(high, low);
	        // },

	        /**
	         * Bitwise XORs this word with the passed word.
	         *
	         * @param {X64Word} word The x64-Word to XOR with this word.
	         *
	         * @return {X64Word} A new x64-Word object after XORing.
	         *
	         * @example
	         *
	         *     var xored = x64Word.xor(anotherX64Word);
	         */
	        // xor: function (word) {
	            // var high = this.high ^ word.high;
	            // var low = this.low ^ word.low;

	            // return X64Word.create(high, low);
	        // },

	        /**
	         * Shifts this word n bits to the left.
	         *
	         * @param {number} n The number of bits to shift.
	         *
	         * @return {X64Word} A new x64-Word object after shifting.
	         *
	         * @example
	         *
	         *     var shifted = x64Word.shiftL(25);
	         */
	        // shiftL: function (n) {
	            // if (n < 32) {
	                // var high = (this.high << n) | (this.low >>> (32 - n));
	                // var low = this.low << n;
	            // } else {
	                // var high = this.low << (n - 32);
	                // var low = 0;
	            // }

	            // return X64Word.create(high, low);
	        // },

	        /**
	         * Shifts this word n bits to the right.
	         *
	         * @param {number} n The number of bits to shift.
	         *
	         * @return {X64Word} A new x64-Word object after shifting.
	         *
	         * @example
	         *
	         *     var shifted = x64Word.shiftR(7);
	         */
	        // shiftR: function (n) {
	            // if (n < 32) {
	                // var low = (this.low >>> n) | (this.high << (32 - n));
	                // var high = this.high >>> n;
	            // } else {
	                // var low = this.high >>> (n - 32);
	                // var high = 0;
	            // }

	            // return X64Word.create(high, low);
	        // },

	        /**
	         * Rotates this word n bits to the left.
	         *
	         * @param {number} n The number of bits to rotate.
	         *
	         * @return {X64Word} A new x64-Word object after rotating.
	         *
	         * @example
	         *
	         *     var rotated = x64Word.rotL(25);
	         */
	        // rotL: function (n) {
	            // return this.shiftL(n).or(this.shiftR(64 - n));
	        // },

	        /**
	         * Rotates this word n bits to the right.
	         *
	         * @param {number} n The number of bits to rotate.
	         *
	         * @return {X64Word} A new x64-Word object after rotating.
	         *
	         * @example
	         *
	         *     var rotated = x64Word.rotR(7);
	         */
	        // rotR: function (n) {
	            // return this.shiftR(n).or(this.shiftL(64 - n));
	        // },

	        /**
	         * Adds this word with the passed word.
	         *
	         * @param {X64Word} word The x64-Word to add with this word.
	         *
	         * @return {X64Word} A new x64-Word object after adding.
	         *
	         * @example
	         *
	         *     var added = x64Word.add(anotherX64Word);
	         */
	        // add: function (word) {
	            // var low = (this.low + word.low) | 0;
	            // var carry = (low >>> 0) < (this.low >>> 0) ? 1 : 0;
	            // var high = (this.high + word.high + carry) | 0;

	            // return X64Word.create(high, low);
	        // }
	    });

	    /**
	     * An array of 64-bit words.
	     *
	     * @property {Array} words The array of CryptoJS.x64.Word objects.
	     * @property {number} sigBytes The number of significant bytes in this word array.
	     */
	    var X64WordArray = C_x64.WordArray = Base.extend({
	        /**
	         * Initializes a newly created word array.
	         *
	         * @param {Array} words (Optional) An array of CryptoJS.x64.Word objects.
	         * @param {number} sigBytes (Optional) The number of significant bytes in the words.
	         *
	         * @example
	         *
	         *     var wordArray = CryptoJS.x64.WordArray.create();
	         *
	         *     var wordArray = CryptoJS.x64.WordArray.create([
	         *         CryptoJS.x64.Word.create(0x00010203, 0x04050607),
	         *         CryptoJS.x64.Word.create(0x18191a1b, 0x1c1d1e1f)
	         *     ]);
	         *
	         *     var wordArray = CryptoJS.x64.WordArray.create([
	         *         CryptoJS.x64.Word.create(0x00010203, 0x04050607),
	         *         CryptoJS.x64.Word.create(0x18191a1b, 0x1c1d1e1f)
	         *     ], 10);
	         */
	        init: function (words, sigBytes) {
	            words = this.words = words || [];

	            if (sigBytes != undefined) {
	                this.sigBytes = sigBytes;
	            } else {
	                this.sigBytes = words.length * 8;
	            }
	        },

	        /**
	         * Converts this 64-bit word array to a 32-bit word array.
	         *
	         * @return {CryptoJS.lib.WordArray} This word array's data as a 32-bit word array.
	         *
	         * @example
	         *
	         *     var x32WordArray = x64WordArray.toX32();
	         */
	        toX32: function () {
	            // Shortcuts
	            var x64Words = this.words;
	            var x64WordsLength = x64Words.length;

	            // Convert
	            var x32Words = [];
	            for (var i = 0; i < x64WordsLength; i++) {
	                var x64Word = x64Words[i];
	                x32Words.push(x64Word.high);
	                x32Words.push(x64Word.low);
	            }

	            return X32WordArray.create(x32Words, this.sigBytes);
	        },

	        /**
	         * Creates a copy of this word array.
	         *
	         * @return {X64WordArray} The clone.
	         *
	         * @example
	         *
	         *     var clone = x64WordArray.clone();
	         */
	        clone: function () {
	            var clone = Base.clone.call(this);

	            // Clone "words" array
	            var words = clone.words = this.words.slice(0);

	            // Clone each X64Word object
	            var wordsLength = words.length;
	            for (var i = 0; i < wordsLength; i++) {
	                words[i] = words[i].clone();
	            }

	            return clone;
	        }
	    });
	}());


	return CryptoJS;

}));

/***/ }),
/* 128 */
/***/ (function(module, exports) {

exports.f = {}.propertyIsEnumerable;


/***/ }),
/* 129 */
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.2 / 15.2.3.5 Object.create(O [, Properties])
var anObject = __webpack_require__(29);
var dPs = __webpack_require__(257);
var enumBugKeys = __webpack_require__(182);
var IE_PROTO = __webpack_require__(180)('IE_PROTO');
var Empty = function () { /* empty */ };
var PROTOTYPE = 'prototype';

// Create object with fake `null` prototype: use iframe Object with cleared prototype
var createDict = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = __webpack_require__(176)('iframe');
  var i = enumBugKeys.length;
  var lt = '<';
  var gt = '>';
  var iframeDocument;
  iframe.style.display = 'none';
  __webpack_require__(260).appendChild(iframe);
  iframe.src = 'javascript:'; // eslint-disable-line no-script-url
  // createDict = iframe.contentWindow.Object;
  // html.removeChild(iframe);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(lt + 'script' + gt + 'document.F=Object' + lt + '/script' + gt);
  iframeDocument.close();
  createDict = iframeDocument.F;
  while (i--) delete createDict[PROTOTYPE][enumBugKeys[i]];
  return createDict();
};

module.exports = Object.create || function create(O, Properties) {
  var result;
  if (O !== null) {
    Empty[PROTOTYPE] = anObject(O);
    result = new Empty();
    Empty[PROTOTYPE] = null;
    // add "__proto__" for Object.getPrototypeOf polyfill
    result[IE_PROTO] = O;
  } else result = createDict();
  return Properties === undefined ? result : dPs(result, Properties);
};


/***/ }),
/* 130 */
/***/ (function(module, exports, __webpack_require__) {

// 7.1.15 ToLength
var toInteger = __webpack_require__(179);
var min = Math.min;
module.exports = function (it) {
  return it > 0 ? min(toInteger(it), 0x1fffffffffffff) : 0; // pow(2, 53) - 1 == 9007199254740991
};


/***/ }),
/* 131 */
/***/ (function(module, exports) {

var id = 0;
var px = Math.random();
module.exports = function (key) {
  return 'Symbol('.concat(key === undefined ? '' : key, ')_', (++id + px).toString(36));
};


/***/ }),
/* 132 */
/***/ (function(module, exports, __webpack_require__) {

var META = __webpack_require__(131)('meta');
var isObject = __webpack_require__(22);
var has = __webpack_require__(51);
var setDesc = __webpack_require__(26).f;
var id = 0;
var isExtensible = Object.isExtensible || function () {
  return true;
};
var FREEZE = !__webpack_require__(52)(function () {
  return isExtensible(Object.preventExtensions({}));
});
var setMeta = function (it) {
  setDesc(it, META, { value: {
    i: 'O' + ++id, // object ID
    w: {}          // weak collections IDs
  } });
};
var fastKey = function (it, create) {
  // return primitive with prefix
  if (!isObject(it)) return typeof it == 'symbol' ? it : (typeof it == 'string' ? 'S' : 'P') + it;
  if (!has(it, META)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible(it)) return 'F';
    // not necessary to add metadata
    if (!create) return 'E';
    // add missing metadata
    setMeta(it);
  // return object ID
  } return it[META].i;
};
var getWeak = function (it, create) {
  if (!has(it, META)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible(it)) return true;
    // not necessary to add metadata
    if (!create) return false;
    // add missing metadata
    setMeta(it);
  // return hash weak collections IDs
  } return it[META].w;
};
// add metadata on freeze-family methods calling
var onFreeze = function (it) {
  if (FREEZE && meta.NEED && isExtensible(it) && !has(it, META)) setMeta(it);
  return it;
};
var meta = module.exports = {
  KEY: META,
  NEED: false,
  fastKey: fastKey,
  getWeak: getWeak,
  onFreeze: onFreeze
};


/***/ }),
/* 133 */
/***/ (function(module, exports) {



/***/ }),
/* 134 */
/***/ (function(module, exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(236);

// `Uint32Array` constructor
// https://tc39.github.io/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Uint32', function (init) {
  return function Uint32Array(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
});


/***/ }),
/* 135 */
/***/ (function(module, exports) {

var TINF_OK = 0;
var TINF_DATA_ERROR = -3;

function Tree() {
  this.table = new Uint16Array(16);   /* table of code length counts */
  this.trans = new Uint16Array(288);  /* code -> symbol translation table */
}

function Data(source, dest) {
  this.source = source;
  this.sourceIndex = 0;
  this.tag = 0;
  this.bitcount = 0;
  
  this.dest = dest;
  this.destLen = 0;
  
  this.ltree = new Tree();  /* dynamic length/symbol tree */
  this.dtree = new Tree();  /* dynamic distance tree */
}

/* --------------------------------------------------- *
 * -- uninitialized global data (static structures) -- *
 * --------------------------------------------------- */

var sltree = new Tree();
var sdtree = new Tree();

/* extra bits and base tables for length codes */
var length_bits = new Uint8Array(30);
var length_base = new Uint16Array(30);

/* extra bits and base tables for distance codes */
var dist_bits = new Uint8Array(30);
var dist_base = new Uint16Array(30);

/* special ordering of code length codes */
var clcidx = new Uint8Array([
  16, 17, 18, 0, 8, 7, 9, 6,
  10, 5, 11, 4, 12, 3, 13, 2,
  14, 1, 15
]);

/* used by tinf_decode_trees, avoids allocations every call */
var code_tree = new Tree();
var lengths = new Uint8Array(288 + 32);

/* ----------------------- *
 * -- utility functions -- *
 * ----------------------- */

/* build extra bits and base tables */
function tinf_build_bits_base(bits, base, delta, first) {
  var i, sum;

  /* build bits table */
  for (i = 0; i < delta; ++i) bits[i] = 0;
  for (i = 0; i < 30 - delta; ++i) bits[i + delta] = i / delta | 0;

  /* build base table */
  for (sum = first, i = 0; i < 30; ++i) {
    base[i] = sum;
    sum += 1 << bits[i];
  }
}

/* build the fixed huffman trees */
function tinf_build_fixed_trees(lt, dt) {
  var i;

  /* build fixed length tree */
  for (i = 0; i < 7; ++i) lt.table[i] = 0;

  lt.table[7] = 24;
  lt.table[8] = 152;
  lt.table[9] = 112;

  for (i = 0; i < 24; ++i) lt.trans[i] = 256 + i;
  for (i = 0; i < 144; ++i) lt.trans[24 + i] = i;
  for (i = 0; i < 8; ++i) lt.trans[24 + 144 + i] = 280 + i;
  for (i = 0; i < 112; ++i) lt.trans[24 + 144 + 8 + i] = 144 + i;

  /* build fixed distance tree */
  for (i = 0; i < 5; ++i) dt.table[i] = 0;

  dt.table[5] = 32;

  for (i = 0; i < 32; ++i) dt.trans[i] = i;
}

/* given an array of code lengths, build a tree */
var offs = new Uint16Array(16);

function tinf_build_tree(t, lengths, off, num) {
  var i, sum;

  /* clear code length count table */
  for (i = 0; i < 16; ++i) t.table[i] = 0;

  /* scan symbol lengths, and sum code length counts */
  for (i = 0; i < num; ++i) t.table[lengths[off + i]]++;

  t.table[0] = 0;

  /* compute offset table for distribution sort */
  for (sum = 0, i = 0; i < 16; ++i) {
    offs[i] = sum;
    sum += t.table[i];
  }

  /* create code->symbol translation table (symbols sorted by code) */
  for (i = 0; i < num; ++i) {
    if (lengths[off + i]) t.trans[offs[lengths[off + i]]++] = i;
  }
}

/* ---------------------- *
 * -- decode functions -- *
 * ---------------------- */

/* get one bit from source stream */
function tinf_getbit(d) {
  /* check if tag is empty */
  if (!d.bitcount--) {
    /* load next tag */
    d.tag = d.source[d.sourceIndex++];
    d.bitcount = 7;
  }

  /* shift bit out of tag */
  var bit = d.tag & 1;
  d.tag >>>= 1;

  return bit;
}

/* read a num bit value from a stream and add base */
function tinf_read_bits(d, num, base) {
  if (!num)
    return base;

  while (d.bitcount < 24) {
    d.tag |= d.source[d.sourceIndex++] << d.bitcount;
    d.bitcount += 8;
  }

  var val = d.tag & (0xffff >>> (16 - num));
  d.tag >>>= num;
  d.bitcount -= num;
  return val + base;
}

/* given a data stream and a tree, decode a symbol */
function tinf_decode_symbol(d, t) {
  while (d.bitcount < 24) {
    d.tag |= d.source[d.sourceIndex++] << d.bitcount;
    d.bitcount += 8;
  }
  
  var sum = 0, cur = 0, len = 0;
  var tag = d.tag;

  /* get more bits while code value is above sum */
  do {
    cur = 2 * cur + (tag & 1);
    tag >>>= 1;
    ++len;

    sum += t.table[len];
    cur -= t.table[len];
  } while (cur >= 0);
  
  d.tag = tag;
  d.bitcount -= len;

  return t.trans[sum + cur];
}

/* given a data stream, decode dynamic trees from it */
function tinf_decode_trees(d, lt, dt) {
  var hlit, hdist, hclen;
  var i, num, length;

  /* get 5 bits HLIT (257-286) */
  hlit = tinf_read_bits(d, 5, 257);

  /* get 5 bits HDIST (1-32) */
  hdist = tinf_read_bits(d, 5, 1);

  /* get 4 bits HCLEN (4-19) */
  hclen = tinf_read_bits(d, 4, 4);

  for (i = 0; i < 19; ++i) lengths[i] = 0;

  /* read code lengths for code length alphabet */
  for (i = 0; i < hclen; ++i) {
    /* get 3 bits code length (0-7) */
    var clen = tinf_read_bits(d, 3, 0);
    lengths[clcidx[i]] = clen;
  }

  /* build code length tree */
  tinf_build_tree(code_tree, lengths, 0, 19);

  /* decode code lengths for the dynamic trees */
  for (num = 0; num < hlit + hdist;) {
    var sym = tinf_decode_symbol(d, code_tree);

    switch (sym) {
      case 16:
        /* copy previous code length 3-6 times (read 2 bits) */
        var prev = lengths[num - 1];
        for (length = tinf_read_bits(d, 2, 3); length; --length) {
          lengths[num++] = prev;
        }
        break;
      case 17:
        /* repeat code length 0 for 3-10 times (read 3 bits) */
        for (length = tinf_read_bits(d, 3, 3); length; --length) {
          lengths[num++] = 0;
        }
        break;
      case 18:
        /* repeat code length 0 for 11-138 times (read 7 bits) */
        for (length = tinf_read_bits(d, 7, 11); length; --length) {
          lengths[num++] = 0;
        }
        break;
      default:
        /* values 0-15 represent the actual code lengths */
        lengths[num++] = sym;
        break;
    }
  }

  /* build dynamic trees */
  tinf_build_tree(lt, lengths, 0, hlit);
  tinf_build_tree(dt, lengths, hlit, hdist);
}

/* ----------------------------- *
 * -- block inflate functions -- *
 * ----------------------------- */

/* given a stream and two trees, inflate a block of data */
function tinf_inflate_block_data(d, lt, dt) {
  while (1) {
    var sym = tinf_decode_symbol(d, lt);

    /* check for end of block */
    if (sym === 256) {
      return TINF_OK;
    }

    if (sym < 256) {
      d.dest[d.destLen++] = sym;
    } else {
      var length, dist, offs;
      var i;

      sym -= 257;

      /* possibly get more bits from length code */
      length = tinf_read_bits(d, length_bits[sym], length_base[sym]);

      dist = tinf_decode_symbol(d, dt);

      /* possibly get more bits from distance code */
      offs = d.destLen - tinf_read_bits(d, dist_bits[dist], dist_base[dist]);

      /* copy match */
      for (i = offs; i < offs + length; ++i) {
        d.dest[d.destLen++] = d.dest[i];
      }
    }
  }
}

/* inflate an uncompressed block of data */
function tinf_inflate_uncompressed_block(d) {
  var length, invlength;
  var i;
  
  /* unread from bitbuffer */
  while (d.bitcount > 8) {
    d.sourceIndex--;
    d.bitcount -= 8;
  }

  /* get length */
  length = d.source[d.sourceIndex + 1];
  length = 256 * length + d.source[d.sourceIndex];

  /* get one's complement of length */
  invlength = d.source[d.sourceIndex + 3];
  invlength = 256 * invlength + d.source[d.sourceIndex + 2];

  /* check length */
  if (length !== (~invlength & 0x0000ffff))
    return TINF_DATA_ERROR;

  d.sourceIndex += 4;

  /* copy block */
  for (i = length; i; --i)
    d.dest[d.destLen++] = d.source[d.sourceIndex++];

  /* make sure we start next block on a byte boundary */
  d.bitcount = 0;

  return TINF_OK;
}

/* inflate stream from source to dest */
function tinf_uncompress(source, dest) {
  var d = new Data(source, dest);
  var bfinal, btype, res;

  do {
    /* read final block flag */
    bfinal = tinf_getbit(d);

    /* read block type (2 bits) */
    btype = tinf_read_bits(d, 2, 0);

    /* decompress block */
    switch (btype) {
      case 0:
        /* decompress uncompressed block */
        res = tinf_inflate_uncompressed_block(d);
        break;
      case 1:
        /* decompress block with fixed huffman trees */
        res = tinf_inflate_block_data(d, sltree, sdtree);
        break;
      case 2:
        /* decompress block with dynamic huffman trees */
        tinf_decode_trees(d, d.ltree, d.dtree);
        res = tinf_inflate_block_data(d, d.ltree, d.dtree);
        break;
      default:
        res = TINF_DATA_ERROR;
    }

    if (res !== TINF_OK)
      throw new Error('Data error');

  } while (!bfinal);

  if (d.destLen < d.dest.length) {
    if (typeof d.dest.slice === 'function')
      return d.dest.slice(0, d.destLen);
    else
      return d.dest.subarray(0, d.destLen);
  }
  
  return d.dest;
}

/* -------------------- *
 * -- initialization -- *
 * -------------------- */

/* build fixed huffman trees */
tinf_build_fixed_trees(sltree, sdtree);

/* build extra bits and base tables */
tinf_build_bits_base(length_bits, length_base, 4, 3);
tinf_build_bits_base(dist_bits, dist_base, 2, 1);

/* fix a special case */
length_bits[28] = 0;
length_base[28] = 258;

module.exports = tinf_uncompress;


/***/ }),
/* 136 */
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__(16);
var isArrayIteratorMethod = __webpack_require__(150);
var toLength = __webpack_require__(8);
var bind = __webpack_require__(96);
var getIteratorMethod = __webpack_require__(151);
var callWithSafeIterationClosing = __webpack_require__(214);

var Result = function (stopped, result) {
  this.stopped = stopped;
  this.result = result;
};

var iterate = module.exports = function (iterable, fn, that, AS_ENTRIES, IS_ITERATOR) {
  var boundFunction = bind(fn, that, AS_ENTRIES ? 2 : 1);
  var iterator, iterFn, index, length, result, next, step;

  if (IS_ITERATOR) {
    iterator = iterable;
  } else {
    iterFn = getIteratorMethod(iterable);
    if (typeof iterFn != 'function') throw TypeError('Target is not iterable');
    // optimisation for array iterators
    if (isArrayIteratorMethod(iterFn)) {
      for (index = 0, length = toLength(iterable.length); length > index; index++) {
        result = AS_ENTRIES
          ? boundFunction(anObject(step = iterable[index])[0], step[1])
          : boundFunction(iterable[index]);
        if (result && result instanceof Result) return result;
      } return new Result(false);
    }
    iterator = iterFn.call(iterable);
  }

  next = iterator.next;
  while (!(step = next.call(iterator)).done) {
    result = callWithSafeIterationClosing(iterator, boundFunction, step.value, AS_ENTRIES);
    if (typeof result == 'object' && result && result instanceof Result) return result;
  } return new Result(false);
};

iterate.stop = function (result) {
  return new Result(true, result);
};


/***/ }),
/* 137 */
/***/ (function(module, exports) {

module.exports = function (exec) {
  try {
    return { error: false, value: exec() };
  } catch (error) {
    return { error: true, value: error };
  }
};


/***/ }),
/* 138 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var isObject = __webpack_require__(13);

var document = global.document;
// typeof document.createElement is 'object' in old IE
var EXISTS = isObject(document) && isObject(document.createElement);

module.exports = function (it) {
  return EXISTS ? document.createElement(it) : {};
};


/***/ }),
/* 139 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var createNonEnumerableProperty = __webpack_require__(18);

module.exports = function (key, value) {
  try {
    createNonEnumerableProperty(global, key, value);
  } catch (error) {
    global[key] = value;
  } return value;
};


/***/ }),
/* 140 */
/***/ (function(module, exports, __webpack_require__) {

var store = __webpack_require__(199);

var functionToString = Function.toString;

// this helper broken in `3.4.1-3.4.4`, so we can't use `shared` helper
if (typeof store.inspectSource != 'function') {
  store.inspectSource = function (it) {
    return functionToString.call(it);
  };
}

module.exports = store.inspectSource;


/***/ }),
/* 141 */
/***/ (function(module, exports, __webpack_require__) {

var IS_PURE = __webpack_require__(56);
var store = __webpack_require__(199);

(module.exports = function (key, value) {
  return store[key] || (store[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: '3.6.5',
  mode: IS_PURE ? 'pure' : 'global',
  copyright: '© 2020 Denis Pushkarev (zloirock.ru)'
});


/***/ }),
/* 142 */
/***/ (function(module, exports) {

// IE8- don't enum bug keys
module.exports = [
  'constructor',
  'hasOwnProperty',
  'isPrototypeOf',
  'propertyIsEnumerable',
  'toLocaleString',
  'toString',
  'valueOf'
];


/***/ }),
/* 143 */
/***/ (function(module, exports) {

exports.f = Object.getOwnPropertySymbols;


/***/ }),
/* 144 */
/***/ (function(module, exports, __webpack_require__) {

var fails = __webpack_require__(4);

var replacement = /#|\.prototype\./;

var isForced = function (feature, detection) {
  var value = data[normalize(feature)];
  return value == POLYFILL ? true
    : value == NATIVE ? false
    : typeof detection == 'function' ? fails(detection)
    : !!detection;
};

var normalize = isForced.normalize = function (string) {
  return String(string).replace(replacement, '.').toLowerCase();
};

var data = isForced.data = {};
var NATIVE = isForced.NATIVE = 'N';
var POLYFILL = isForced.POLYFILL = 'P';

module.exports = isForced;


/***/ }),
/* 145 */
/***/ (function(module, exports, __webpack_require__) {

var fails = __webpack_require__(4);

module.exports = !!Object.getOwnPropertySymbols && !fails(function () {
  // Chrome 38 Symbol has incorrect toString conversion
  // eslint-disable-next-line no-undef
  return !String(Symbol());
});


/***/ }),
/* 146 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var toPrimitive = __webpack_require__(55);
var definePropertyModule = __webpack_require__(15);
var createPropertyDescriptor = __webpack_require__(42);

module.exports = function (object, key, value) {
  var propertyKey = toPrimitive(key);
  if (propertyKey in object) definePropertyModule.f(object, propertyKey, createPropertyDescriptor(0, value));
  else object[propertyKey] = value;
};


/***/ }),
/* 147 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var userAgent = __webpack_require__(210);

var process = global.process;
var versions = process && process.versions;
var v8 = versions && versions.v8;
var match, version;

if (v8) {
  match = v8.split('.');
  version = match[0] + match[1];
} else if (userAgent) {
  match = userAgent.match(/Edge\/(\d+)/);
  if (!match || match[1] >= 74) {
    match = userAgent.match(/Chrome\/(\d+)/);
    if (match) version = match[1];
  }
}

module.exports = version && +version;


/***/ }),
/* 148 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var toObject = __webpack_require__(19);
var toAbsoluteIndex = __webpack_require__(43);
var toLength = __webpack_require__(8);

// `Array.prototype.fill` method implementation
// https://tc39.github.io/ecma262/#sec-array.prototype.fill
module.exports = function fill(value /* , start = 0, end = @length */) {
  var O = toObject(this);
  var length = toLength(O.length);
  var argumentsLength = arguments.length;
  var index = toAbsoluteIndex(argumentsLength > 1 ? arguments[1] : undefined, length);
  var end = argumentsLength > 2 ? arguments[2] : undefined;
  var endPos = end === undefined ? length : toAbsoluteIndex(end, length);
  while (endPos > index) O[index++] = value;
  return O;
};


/***/ }),
/* 149 */
/***/ (function(module, exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(6);
var create = __webpack_require__(58);
var definePropertyModule = __webpack_require__(15);

var UNSCOPABLES = wellKnownSymbol('unscopables');
var ArrayPrototype = Array.prototype;

// Array.prototype[@@unscopables]
// https://tc39.github.io/ecma262/#sec-array.prototype-@@unscopables
if (ArrayPrototype[UNSCOPABLES] == undefined) {
  definePropertyModule.f(ArrayPrototype, UNSCOPABLES, {
    configurable: true,
    value: create(null)
  });
}

// add a key to Array.prototype[@@unscopables]
module.exports = function (key) {
  ArrayPrototype[UNSCOPABLES][key] = true;
};


/***/ }),
/* 150 */
/***/ (function(module, exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(6);
var Iterators = __webpack_require__(97);

var ITERATOR = wellKnownSymbol('iterator');
var ArrayPrototype = Array.prototype;

// check on default Array iterator
module.exports = function (it) {
  return it !== undefined && (Iterators.Array === it || ArrayPrototype[ITERATOR] === it);
};


/***/ }),
/* 151 */
/***/ (function(module, exports, __webpack_require__) {

var classof = __webpack_require__(117);
var Iterators = __webpack_require__(97);
var wellKnownSymbol = __webpack_require__(6);

var ITERATOR = wellKnownSymbol('iterator');

module.exports = function (it) {
  if (it != undefined) return it[ITERATOR]
    || it['@@iterator']
    || Iterators[classof(it)];
};


/***/ }),
/* 152 */
/***/ (function(module, exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(6);

var TO_STRING_TAG = wellKnownSymbol('toStringTag');
var test = {};

test[TO_STRING_TAG] = 'z';

module.exports = String(test) === '[object z]';


/***/ }),
/* 153 */
/***/ (function(module, exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(6);

var ITERATOR = wellKnownSymbol('iterator');
var SAFE_CLOSING = false;

try {
  var called = 0;
  var iteratorWithReturn = {
    next: function () {
      return { done: !!called++ };
    },
    'return': function () {
      SAFE_CLOSING = true;
    }
  };
  iteratorWithReturn[ITERATOR] = function () {
    return this;
  };
  // eslint-disable-next-line no-throw-literal
  Array.from(iteratorWithReturn, function () { throw 2; });
} catch (error) { /* empty */ }

module.exports = function (exec, SKIP_CLOSING) {
  if (!SKIP_CLOSING && !SAFE_CLOSING) return false;
  var ITERATION_SUPPORT = false;
  try {
    var object = {};
    object[ITERATOR] = function () {
      return {
        next: function () {
          return { done: ITERATION_SUPPORT = true };
        }
      };
    };
    exec(object);
  } catch (error) { /* empty */ }
  return ITERATION_SUPPORT;
};


/***/ }),
/* 154 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(3);
var DESCRIPTORS = __webpack_require__(11);
var NATIVE_ARRAY_BUFFER = __webpack_require__(220);
var createNonEnumerableProperty = __webpack_require__(18);
var redefineAll = __webpack_require__(221);
var fails = __webpack_require__(4);
var anInstance = __webpack_require__(155);
var toInteger = __webpack_require__(37);
var toLength = __webpack_require__(8);
var toIndex = __webpack_require__(222);
var IEEE754 = __webpack_require__(321);
var getPrototypeOf = __webpack_require__(60);
var setPrototypeOf = __webpack_require__(45);
var getOwnPropertyNames = __webpack_require__(57).f;
var defineProperty = __webpack_require__(15).f;
var arrayFill = __webpack_require__(148);
var setToStringTag = __webpack_require__(95);
var InternalStateModule = __webpack_require__(35);

var getInternalState = InternalStateModule.get;
var setInternalState = InternalStateModule.set;
var ARRAY_BUFFER = 'ArrayBuffer';
var DATA_VIEW = 'DataView';
var PROTOTYPE = 'prototype';
var WRONG_LENGTH = 'Wrong length';
var WRONG_INDEX = 'Wrong index';
var NativeArrayBuffer = global[ARRAY_BUFFER];
var $ArrayBuffer = NativeArrayBuffer;
var $DataView = global[DATA_VIEW];
var $DataViewPrototype = $DataView && $DataView[PROTOTYPE];
var ObjectPrototype = Object.prototype;
var RangeError = global.RangeError;

var packIEEE754 = IEEE754.pack;
var unpackIEEE754 = IEEE754.unpack;

var packInt8 = function (number) {
  return [number & 0xFF];
};

var packInt16 = function (number) {
  return [number & 0xFF, number >> 8 & 0xFF];
};

var packInt32 = function (number) {
  return [number & 0xFF, number >> 8 & 0xFF, number >> 16 & 0xFF, number >> 24 & 0xFF];
};

var unpackInt32 = function (buffer) {
  return buffer[3] << 24 | buffer[2] << 16 | buffer[1] << 8 | buffer[0];
};

var packFloat32 = function (number) {
  return packIEEE754(number, 23, 4);
};

var packFloat64 = function (number) {
  return packIEEE754(number, 52, 8);
};

var addGetter = function (Constructor, key) {
  defineProperty(Constructor[PROTOTYPE], key, { get: function () { return getInternalState(this)[key]; } });
};

var get = function (view, count, index, isLittleEndian) {
  var intIndex = toIndex(index);
  var store = getInternalState(view);
  if (intIndex + count > store.byteLength) throw RangeError(WRONG_INDEX);
  var bytes = getInternalState(store.buffer).bytes;
  var start = intIndex + store.byteOffset;
  var pack = bytes.slice(start, start + count);
  return isLittleEndian ? pack : pack.reverse();
};

var set = function (view, count, index, conversion, value, isLittleEndian) {
  var intIndex = toIndex(index);
  var store = getInternalState(view);
  if (intIndex + count > store.byteLength) throw RangeError(WRONG_INDEX);
  var bytes = getInternalState(store.buffer).bytes;
  var start = intIndex + store.byteOffset;
  var pack = conversion(+value);
  for (var i = 0; i < count; i++) bytes[start + i] = pack[isLittleEndian ? i : count - i - 1];
};

if (!NATIVE_ARRAY_BUFFER) {
  $ArrayBuffer = function ArrayBuffer(length) {
    anInstance(this, $ArrayBuffer, ARRAY_BUFFER);
    var byteLength = toIndex(length);
    setInternalState(this, {
      bytes: arrayFill.call(new Array(byteLength), 0),
      byteLength: byteLength
    });
    if (!DESCRIPTORS) this.byteLength = byteLength;
  };

  $DataView = function DataView(buffer, byteOffset, byteLength) {
    anInstance(this, $DataView, DATA_VIEW);
    anInstance(buffer, $ArrayBuffer, DATA_VIEW);
    var bufferLength = getInternalState(buffer).byteLength;
    var offset = toInteger(byteOffset);
    if (offset < 0 || offset > bufferLength) throw RangeError('Wrong offset');
    byteLength = byteLength === undefined ? bufferLength - offset : toLength(byteLength);
    if (offset + byteLength > bufferLength) throw RangeError(WRONG_LENGTH);
    setInternalState(this, {
      buffer: buffer,
      byteLength: byteLength,
      byteOffset: offset
    });
    if (!DESCRIPTORS) {
      this.buffer = buffer;
      this.byteLength = byteLength;
      this.byteOffset = offset;
    }
  };

  if (DESCRIPTORS) {
    addGetter($ArrayBuffer, 'byteLength');
    addGetter($DataView, 'buffer');
    addGetter($DataView, 'byteLength');
    addGetter($DataView, 'byteOffset');
  }

  redefineAll($DataView[PROTOTYPE], {
    getInt8: function getInt8(byteOffset) {
      return get(this, 1, byteOffset)[0] << 24 >> 24;
    },
    getUint8: function getUint8(byteOffset) {
      return get(this, 1, byteOffset)[0];
    },
    getInt16: function getInt16(byteOffset /* , littleEndian */) {
      var bytes = get(this, 2, byteOffset, arguments.length > 1 ? arguments[1] : undefined);
      return (bytes[1] << 8 | bytes[0]) << 16 >> 16;
    },
    getUint16: function getUint16(byteOffset /* , littleEndian */) {
      var bytes = get(this, 2, byteOffset, arguments.length > 1 ? arguments[1] : undefined);
      return bytes[1] << 8 | bytes[0];
    },
    getInt32: function getInt32(byteOffset /* , littleEndian */) {
      return unpackInt32(get(this, 4, byteOffset, arguments.length > 1 ? arguments[1] : undefined));
    },
    getUint32: function getUint32(byteOffset /* , littleEndian */) {
      return unpackInt32(get(this, 4, byteOffset, arguments.length > 1 ? arguments[1] : undefined)) >>> 0;
    },
    getFloat32: function getFloat32(byteOffset /* , littleEndian */) {
      return unpackIEEE754(get(this, 4, byteOffset, arguments.length > 1 ? arguments[1] : undefined), 23);
    },
    getFloat64: function getFloat64(byteOffset /* , littleEndian */) {
      return unpackIEEE754(get(this, 8, byteOffset, arguments.length > 1 ? arguments[1] : undefined), 52);
    },
    setInt8: function setInt8(byteOffset, value) {
      set(this, 1, byteOffset, packInt8, value);
    },
    setUint8: function setUint8(byteOffset, value) {
      set(this, 1, byteOffset, packInt8, value);
    },
    setInt16: function setInt16(byteOffset, value /* , littleEndian */) {
      set(this, 2, byteOffset, packInt16, value, arguments.length > 2 ? arguments[2] : undefined);
    },
    setUint16: function setUint16(byteOffset, value /* , littleEndian */) {
      set(this, 2, byteOffset, packInt16, value, arguments.length > 2 ? arguments[2] : undefined);
    },
    setInt32: function setInt32(byteOffset, value /* , littleEndian */) {
      set(this, 4, byteOffset, packInt32, value, arguments.length > 2 ? arguments[2] : undefined);
    },
    setUint32: function setUint32(byteOffset, value /* , littleEndian */) {
      set(this, 4, byteOffset, packInt32, value, arguments.length > 2 ? arguments[2] : undefined);
    },
    setFloat32: function setFloat32(byteOffset, value /* , littleEndian */) {
      set(this, 4, byteOffset, packFloat32, value, arguments.length > 2 ? arguments[2] : undefined);
    },
    setFloat64: function setFloat64(byteOffset, value /* , littleEndian */) {
      set(this, 8, byteOffset, packFloat64, value, arguments.length > 2 ? arguments[2] : undefined);
    }
  });
} else {
  if (!fails(function () {
    NativeArrayBuffer(1);
  }) || !fails(function () {
    new NativeArrayBuffer(-1); // eslint-disable-line no-new
  }) || fails(function () {
    new NativeArrayBuffer(); // eslint-disable-line no-new
    new NativeArrayBuffer(1.5); // eslint-disable-line no-new
    new NativeArrayBuffer(NaN); // eslint-disable-line no-new
    return NativeArrayBuffer.name != ARRAY_BUFFER;
  })) {
    $ArrayBuffer = function ArrayBuffer(length) {
      anInstance(this, $ArrayBuffer);
      return new NativeArrayBuffer(toIndex(length));
    };
    var ArrayBufferPrototype = $ArrayBuffer[PROTOTYPE] = NativeArrayBuffer[PROTOTYPE];
    for (var keys = getOwnPropertyNames(NativeArrayBuffer), j = 0, key; keys.length > j;) {
      if (!((key = keys[j++]) in $ArrayBuffer)) {
        createNonEnumerableProperty($ArrayBuffer, key, NativeArrayBuffer[key]);
      }
    }
    ArrayBufferPrototype.constructor = $ArrayBuffer;
  }

  // WebKit bug - the same parent prototype for typed arrays and data view
  if (setPrototypeOf && getPrototypeOf($DataViewPrototype) !== ObjectPrototype) {
    setPrototypeOf($DataViewPrototype, ObjectPrototype);
  }

  // iOS Safari 7.x bug
  var testView = new $DataView(new $ArrayBuffer(2));
  var nativeSetInt8 = $DataViewPrototype.setInt8;
  testView.setInt8(0, 2147483648);
  testView.setInt8(1, 2147483649);
  if (testView.getInt8(0) || !testView.getInt8(1)) redefineAll($DataViewPrototype, {
    setInt8: function setInt8(byteOffset, value) {
      nativeSetInt8.call(this, byteOffset, value << 24 >> 24);
    },
    setUint8: function setUint8(byteOffset, value) {
      nativeSetInt8.call(this, byteOffset, value << 24 >> 24);
    }
  }, { unsafe: true });
}

setToStringTag($ArrayBuffer, ARRAY_BUFFER);
setToStringTag($DataView, DATA_VIEW);

module.exports = {
  ArrayBuffer: $ArrayBuffer,
  DataView: $DataView
};


/***/ }),
/* 155 */
/***/ (function(module, exports) {

module.exports = function (it, Constructor, name) {
  if (!(it instanceof Constructor)) {
    throw TypeError('Incorrect ' + (name ? name + ' ' : '') + 'invocation');
  } return it;
};


/***/ }),
/* 156 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var getBuiltIn = __webpack_require__(36);
var definePropertyModule = __webpack_require__(15);
var wellKnownSymbol = __webpack_require__(6);
var DESCRIPTORS = __webpack_require__(11);

var SPECIES = wellKnownSymbol('species');

module.exports = function (CONSTRUCTOR_NAME) {
  var Constructor = getBuiltIn(CONSTRUCTOR_NAME);
  var defineProperty = definePropertyModule.f;

  if (DESCRIPTORS && Constructor && !Constructor[SPECIES]) {
    defineProperty(Constructor, SPECIES, {
      configurable: true,
      get: function () { return this; }
    });
  }
};


/***/ }),
/* 157 */
/***/ (function(module, exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(11);
var defineProperty = __webpack_require__(15).f;

var FunctionPrototype = Function.prototype;
var FunctionPrototypeToString = FunctionPrototype.toString;
var nameRE = /^\s*function ([^ (]*)/;
var NAME = 'name';

// Function instances `.name` property
// https://tc39.github.io/ecma262/#sec-function-instances-name
if (DESCRIPTORS && !(NAME in FunctionPrototype)) {
  defineProperty(FunctionPrototype, NAME, {
    configurable: true,
    get: function () {
      try {
        return FunctionPrototypeToString.call(this).match(nameRE)[1];
      } catch (error) {
        return '';
      }
    }
  });
}


/***/ }),
/* 158 */
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__(37);
var requireObjectCoercible = __webpack_require__(34);

// `String.prototype.{ codePointAt, at }` methods implementation
var createMethod = function (CONVERT_TO_STRING) {
  return function ($this, pos) {
    var S = String(requireObjectCoercible($this));
    var position = toInteger(pos);
    var size = S.length;
    var first, second;
    if (position < 0 || position >= size) return CONVERT_TO_STRING ? '' : undefined;
    first = S.charCodeAt(position);
    return first < 0xD800 || first > 0xDBFF || position + 1 === size
      || (second = S.charCodeAt(position + 1)) < 0xDC00 || second > 0xDFFF
        ? CONVERT_TO_STRING ? S.charAt(position) : first
        : CONVERT_TO_STRING ? S.slice(position, position + 2) : (first - 0xD800 << 10) + (second - 0xDC00) + 0x10000;
  };
};

module.exports = {
  // `String.prototype.codePointAt` method
  // https://tc39.github.io/ecma262/#sec-string.prototype.codepointat
  codeAt: createMethod(false),
  // `String.prototype.at` method
  // https://github.com/mathiasbynens/String.prototype.at
  charAt: createMethod(true)
};


/***/ }),
/* 159 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var charAt = __webpack_require__(158).charAt;
var InternalStateModule = __webpack_require__(35);
var defineIterator = __webpack_require__(216);

var STRING_ITERATOR = 'String Iterator';
var setInternalState = InternalStateModule.set;
var getInternalState = InternalStateModule.getterFor(STRING_ITERATOR);

// `String.prototype[@@iterator]` method
// https://tc39.github.io/ecma262/#sec-string.prototype-@@iterator
defineIterator(String, 'String', function (iterated) {
  setInternalState(this, {
    type: STRING_ITERATOR,
    string: String(iterated),
    index: 0
  });
// `%StringIteratorPrototype%.next` method
// https://tc39.github.io/ecma262/#sec-%stringiteratorprototype%.next
}, function next() {
  var state = getInternalState(this);
  var string = state.string;
  var index = state.index;
  var point;
  if (index >= string.length) return { value: undefined, done: true };
  point = charAt(string, index);
  state.index += point.length;
  return { value: point, done: false };
});


/***/ }),
/* 160 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var fixRegExpWellKnownSymbolLogic = __webpack_require__(161);
var anObject = __webpack_require__(16);
var toLength = __webpack_require__(8);
var requireObjectCoercible = __webpack_require__(34);
var advanceStringIndex = __webpack_require__(162);
var regExpExec = __webpack_require__(163);

// @@match logic
fixRegExpWellKnownSymbolLogic('match', 1, function (MATCH, nativeMatch, maybeCallNative) {
  return [
    // `String.prototype.match` method
    // https://tc39.github.io/ecma262/#sec-string.prototype.match
    function match(regexp) {
      var O = requireObjectCoercible(this);
      var matcher = regexp == undefined ? undefined : regexp[MATCH];
      return matcher !== undefined ? matcher.call(regexp, O) : new RegExp(regexp)[MATCH](String(O));
    },
    // `RegExp.prototype[@@match]` method
    // https://tc39.github.io/ecma262/#sec-regexp.prototype-@@match
    function (regexp) {
      var res = maybeCallNative(nativeMatch, regexp, this);
      if (res.done) return res.value;

      var rx = anObject(regexp);
      var S = String(this);

      if (!rx.global) return regExpExec(rx, S);

      var fullUnicode = rx.unicode;
      rx.lastIndex = 0;
      var A = [];
      var n = 0;
      var result;
      while ((result = regExpExec(rx, S)) !== null) {
        var matchStr = String(result[0]);
        A[n] = matchStr;
        if (matchStr === '') rx.lastIndex = advanceStringIndex(S, toLength(rx.lastIndex), fullUnicode);
        n++;
      }
      return n === 0 ? null : A;
    }
  ];
});


/***/ }),
/* 161 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

// TODO: Remove from `core-js@4` since it's moved to entry points
__webpack_require__(118);
var redefine = __webpack_require__(25);
var fails = __webpack_require__(4);
var wellKnownSymbol = __webpack_require__(6);
var regexpExec = __webpack_require__(119);
var createNonEnumerableProperty = __webpack_require__(18);

var SPECIES = wellKnownSymbol('species');

var REPLACE_SUPPORTS_NAMED_GROUPS = !fails(function () {
  // #replace needs built-in support for named groups.
  // #match works fine because it just return the exec results, even if it has
  // a "grops" property.
  var re = /./;
  re.exec = function () {
    var result = [];
    result.groups = { a: '7' };
    return result;
  };
  return ''.replace(re, '$<a>') !== '7';
});

// IE <= 11 replaces $0 with the whole match, as if it was $&
// https://stackoverflow.com/questions/6024666/getting-ie-to-replace-a-regex-with-the-literal-string-0
var REPLACE_KEEPS_$0 = (function () {
  return 'a'.replace(/./, '$0') === '$0';
})();

var REPLACE = wellKnownSymbol('replace');
// Safari <= 13.0.3(?) substitutes nth capture where n>m with an empty string
var REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE = (function () {
  if (/./[REPLACE]) {
    return /./[REPLACE]('a', '$0') === '';
  }
  return false;
})();

// Chrome 51 has a buggy "split" implementation when RegExp#exec !== nativeExec
// Weex JS has frozen built-in prototypes, so use try / catch wrapper
var SPLIT_WORKS_WITH_OVERWRITTEN_EXEC = !fails(function () {
  var re = /(?:)/;
  var originalExec = re.exec;
  re.exec = function () { return originalExec.apply(this, arguments); };
  var result = 'ab'.split(re);
  return result.length !== 2 || result[0] !== 'a' || result[1] !== 'b';
});

module.exports = function (KEY, length, exec, sham) {
  var SYMBOL = wellKnownSymbol(KEY);

  var DELEGATES_TO_SYMBOL = !fails(function () {
    // String methods call symbol-named RegEp methods
    var O = {};
    O[SYMBOL] = function () { return 7; };
    return ''[KEY](O) != 7;
  });

  var DELEGATES_TO_EXEC = DELEGATES_TO_SYMBOL && !fails(function () {
    // Symbol-named RegExp methods call .exec
    var execCalled = false;
    var re = /a/;

    if (KEY === 'split') {
      // We can't use real regex here since it causes deoptimization
      // and serious performance degradation in V8
      // https://github.com/zloirock/core-js/issues/306
      re = {};
      // RegExp[@@split] doesn't call the regex's exec method, but first creates
      // a new one. We need to return the patched regex when creating the new one.
      re.constructor = {};
      re.constructor[SPECIES] = function () { return re; };
      re.flags = '';
      re[SYMBOL] = /./[SYMBOL];
    }

    re.exec = function () { execCalled = true; return null; };

    re[SYMBOL]('');
    return !execCalled;
  });

  if (
    !DELEGATES_TO_SYMBOL ||
    !DELEGATES_TO_EXEC ||
    (KEY === 'replace' && !(
      REPLACE_SUPPORTS_NAMED_GROUPS &&
      REPLACE_KEEPS_$0 &&
      !REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE
    )) ||
    (KEY === 'split' && !SPLIT_WORKS_WITH_OVERWRITTEN_EXEC)
  ) {
    var nativeRegExpMethod = /./[SYMBOL];
    var methods = exec(SYMBOL, ''[KEY], function (nativeMethod, regexp, str, arg2, forceStringMethod) {
      if (regexp.exec === regexpExec) {
        if (DELEGATES_TO_SYMBOL && !forceStringMethod) {
          // The native String method already delegates to @@method (this
          // polyfilled function), leasing to infinite recursion.
          // We avoid it by directly calling the native @@method method.
          return { done: true, value: nativeRegExpMethod.call(regexp, str, arg2) };
        }
        return { done: true, value: nativeMethod.call(str, regexp, arg2) };
      }
      return { done: false };
    }, {
      REPLACE_KEEPS_$0: REPLACE_KEEPS_$0,
      REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE: REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE
    });
    var stringMethod = methods[0];
    var regexMethod = methods[1];

    redefine(String.prototype, KEY, stringMethod);
    redefine(RegExp.prototype, SYMBOL, length == 2
      // 21.2.5.8 RegExp.prototype[@@replace](string, replaceValue)
      // 21.2.5.11 RegExp.prototype[@@split](string, limit)
      ? function (string, arg) { return regexMethod.call(string, this, arg); }
      // 21.2.5.6 RegExp.prototype[@@match](string)
      // 21.2.5.9 RegExp.prototype[@@search](string)
      : function (string) { return regexMethod.call(string, this); }
    );
  }

  if (sham) createNonEnumerableProperty(RegExp.prototype[SYMBOL], 'sham', true);
};


/***/ }),
/* 162 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var charAt = __webpack_require__(158).charAt;

// `AdvanceStringIndex` abstract operation
// https://tc39.github.io/ecma262/#sec-advancestringindex
module.exports = function (S, index, unicode) {
  return index + (unicode ? charAt(S, index).length : 1);
};


/***/ }),
/* 163 */
/***/ (function(module, exports, __webpack_require__) {

var classof = __webpack_require__(30);
var regexpExec = __webpack_require__(119);

// `RegExpExec` abstract operation
// https://tc39.github.io/ecma262/#sec-regexpexec
module.exports = function (R, S) {
  var exec = R.exec;
  if (typeof exec === 'function') {
    var result = exec.call(R, S);
    if (typeof result !== 'object') {
      throw TypeError('RegExp exec method returned something other than an Object or null');
    }
    return result;
  }

  if (classof(R) !== 'RegExp') {
    throw TypeError('RegExp#exec called on incompatible receiver');
  }

  return regexpExec.call(R, S);
};



/***/ }),
/* 164 */
/***/ (function(module, exports, __webpack_require__) {

exports = module.exports = __webpack_require__(240);
exports.Stream = exports;
exports.Readable = exports;
exports.Writable = __webpack_require__(165);
exports.Duplex = __webpack_require__(48);
exports.Transform = __webpack_require__(243);
exports.PassThrough = __webpack_require__(345);


/***/ }),
/* 165 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(process, global) {// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

// A bit simpler than readable streams.
// Implement an async ._write(chunk, encoding, cb), and it'll handle all
// the drain event emission and buffering.



/*<replacement>*/

var pna = __webpack_require__(123);
/*</replacement>*/

module.exports = Writable;

/* <replacement> */
function WriteReq(chunk, encoding, cb) {
  this.chunk = chunk;
  this.encoding = encoding;
  this.callback = cb;
  this.next = null;
}

// It seems a linked list but it is not
// there will be only 2 of these for each stream
function CorkedRequest(state) {
  var _this = this;

  this.next = null;
  this.entry = null;
  this.finish = function () {
    onCorkedFinish(_this, state);
  };
}
/* </replacement> */

/*<replacement>*/
var asyncWrite = !process.browser && ['v0.10', 'v0.9.'].indexOf(process.version.slice(0, 5)) > -1 ? setImmediate : pna.nextTick;
/*</replacement>*/

/*<replacement>*/
var Duplex;
/*</replacement>*/

Writable.WritableState = WritableState;

/*<replacement>*/
var util = Object.create(__webpack_require__(98));
util.inherits = __webpack_require__(86);
/*</replacement>*/

/*<replacement>*/
var internalUtil = {
  deprecate: __webpack_require__(344)
};
/*</replacement>*/

/*<replacement>*/
var Stream = __webpack_require__(241);
/*</replacement>*/

/*<replacement>*/

var Buffer = __webpack_require__(124).Buffer;
var OurUint8Array = global.Uint8Array || function () {};
function _uint8ArrayToBuffer(chunk) {
  return Buffer.from(chunk);
}
function _isUint8Array(obj) {
  return Buffer.isBuffer(obj) || obj instanceof OurUint8Array;
}

/*</replacement>*/

var destroyImpl = __webpack_require__(242);

util.inherits(Writable, Stream);

function nop() {}

function WritableState(options, stream) {
  Duplex = Duplex || __webpack_require__(48);

  options = options || {};

  // Duplex streams are both readable and writable, but share
  // the same options object.
  // However, some cases require setting options to different
  // values for the readable and the writable sides of the duplex stream.
  // These options can be provided separately as readableXXX and writableXXX.
  var isDuplex = stream instanceof Duplex;

  // object stream flag to indicate whether or not this stream
  // contains buffers or objects.
  this.objectMode = !!options.objectMode;

  if (isDuplex) this.objectMode = this.objectMode || !!options.writableObjectMode;

  // the point at which write() starts returning false
  // Note: 0 is a valid value, means that we always return false if
  // the entire buffer is not flushed immediately on write()
  var hwm = options.highWaterMark;
  var writableHwm = options.writableHighWaterMark;
  var defaultHwm = this.objectMode ? 16 : 16 * 1024;

  if (hwm || hwm === 0) this.highWaterMark = hwm;else if (isDuplex && (writableHwm || writableHwm === 0)) this.highWaterMark = writableHwm;else this.highWaterMark = defaultHwm;

  // cast to ints.
  this.highWaterMark = Math.floor(this.highWaterMark);

  // if _final has been called
  this.finalCalled = false;

  // drain event flag.
  this.needDrain = false;
  // at the start of calling end()
  this.ending = false;
  // when end() has been called, and returned
  this.ended = false;
  // when 'finish' is emitted
  this.finished = false;

  // has it been destroyed
  this.destroyed = false;

  // should we decode strings into buffers before passing to _write?
  // this is here so that some node-core streams can optimize string
  // handling at a lower level.
  var noDecode = options.decodeStrings === false;
  this.decodeStrings = !noDecode;

  // Crypto is kind of old and crusty.  Historically, its default string
  // encoding is 'binary' so we have to make this configurable.
  // Everything else in the universe uses 'utf8', though.
  this.defaultEncoding = options.defaultEncoding || 'utf8';

  // not an actual buffer we keep track of, but a measurement
  // of how much we're waiting to get pushed to some underlying
  // socket or file.
  this.length = 0;

  // a flag to see when we're in the middle of a write.
  this.writing = false;

  // when true all writes will be buffered until .uncork() call
  this.corked = 0;

  // a flag to be able to tell if the onwrite cb is called immediately,
  // or on a later tick.  We set this to true at first, because any
  // actions that shouldn't happen until "later" should generally also
  // not happen before the first write call.
  this.sync = true;

  // a flag to know if we're processing previously buffered items, which
  // may call the _write() callback in the same tick, so that we don't
  // end up in an overlapped onwrite situation.
  this.bufferProcessing = false;

  // the callback that's passed to _write(chunk,cb)
  this.onwrite = function (er) {
    onwrite(stream, er);
  };

  // the callback that the user supplies to write(chunk,encoding,cb)
  this.writecb = null;

  // the amount that is being written when _write is called.
  this.writelen = 0;

  this.bufferedRequest = null;
  this.lastBufferedRequest = null;

  // number of pending user-supplied write callbacks
  // this must be 0 before 'finish' can be emitted
  this.pendingcb = 0;

  // emit prefinish if the only thing we're waiting for is _write cbs
  // This is relevant for synchronous Transform streams
  this.prefinished = false;

  // True if the error was already emitted and should not be thrown again
  this.errorEmitted = false;

  // count buffered requests
  this.bufferedRequestCount = 0;

  // allocate the first CorkedRequest, there is always
  // one allocated and free to use, and we maintain at most two
  this.corkedRequestsFree = new CorkedRequest(this);
}

WritableState.prototype.getBuffer = function getBuffer() {
  var current = this.bufferedRequest;
  var out = [];
  while (current) {
    out.push(current);
    current = current.next;
  }
  return out;
};

(function () {
  try {
    Object.defineProperty(WritableState.prototype, 'buffer', {
      get: internalUtil.deprecate(function () {
        return this.getBuffer();
      }, '_writableState.buffer is deprecated. Use _writableState.getBuffer ' + 'instead.', 'DEP0003')
    });
  } catch (_) {}
})();

// Test _writableState for inheritance to account for Duplex streams,
// whose prototype chain only points to Readable.
var realHasInstance;
if (typeof Symbol === 'function' && Symbol.hasInstance && typeof Function.prototype[Symbol.hasInstance] === 'function') {
  realHasInstance = Function.prototype[Symbol.hasInstance];
  Object.defineProperty(Writable, Symbol.hasInstance, {
    value: function (object) {
      if (realHasInstance.call(this, object)) return true;
      if (this !== Writable) return false;

      return object && object._writableState instanceof WritableState;
    }
  });
} else {
  realHasInstance = function (object) {
    return object instanceof this;
  };
}

function Writable(options) {
  Duplex = Duplex || __webpack_require__(48);

  // Writable ctor is applied to Duplexes, too.
  // `realHasInstance` is necessary because using plain `instanceof`
  // would return false, as no `_writableState` property is attached.

  // Trying to use the custom `instanceof` for Writable here will also break the
  // Node.js LazyTransform implementation, which has a non-trivial getter for
  // `_writableState` that would lead to infinite recursion.
  if (!realHasInstance.call(Writable, this) && !(this instanceof Duplex)) {
    return new Writable(options);
  }

  this._writableState = new WritableState(options, this);

  // legacy.
  this.writable = true;

  if (options) {
    if (typeof options.write === 'function') this._write = options.write;

    if (typeof options.writev === 'function') this._writev = options.writev;

    if (typeof options.destroy === 'function') this._destroy = options.destroy;

    if (typeof options.final === 'function') this._final = options.final;
  }

  Stream.call(this);
}

// Otherwise people can pipe Writable streams, which is just wrong.
Writable.prototype.pipe = function () {
  this.emit('error', new Error('Cannot pipe, not readable'));
};

function writeAfterEnd(stream, cb) {
  var er = new Error('write after end');
  // TODO: defer error events consistently everywhere, not just the cb
  stream.emit('error', er);
  pna.nextTick(cb, er);
}

// Checks that a user-supplied chunk is valid, especially for the particular
// mode the stream is in. Currently this means that `null` is never accepted
// and undefined/non-string values are only allowed in object mode.
function validChunk(stream, state, chunk, cb) {
  var valid = true;
  var er = false;

  if (chunk === null) {
    er = new TypeError('May not write null values to stream');
  } else if (typeof chunk !== 'string' && chunk !== undefined && !state.objectMode) {
    er = new TypeError('Invalid non-string/buffer chunk');
  }
  if (er) {
    stream.emit('error', er);
    pna.nextTick(cb, er);
    valid = false;
  }
  return valid;
}

Writable.prototype.write = function (chunk, encoding, cb) {
  var state = this._writableState;
  var ret = false;
  var isBuf = !state.objectMode && _isUint8Array(chunk);

  if (isBuf && !Buffer.isBuffer(chunk)) {
    chunk = _uint8ArrayToBuffer(chunk);
  }

  if (typeof encoding === 'function') {
    cb = encoding;
    encoding = null;
  }

  if (isBuf) encoding = 'buffer';else if (!encoding) encoding = state.defaultEncoding;

  if (typeof cb !== 'function') cb = nop;

  if (state.ended) writeAfterEnd(this, cb);else if (isBuf || validChunk(this, state, chunk, cb)) {
    state.pendingcb++;
    ret = writeOrBuffer(this, state, isBuf, chunk, encoding, cb);
  }

  return ret;
};

Writable.prototype.cork = function () {
  var state = this._writableState;

  state.corked++;
};

Writable.prototype.uncork = function () {
  var state = this._writableState;

  if (state.corked) {
    state.corked--;

    if (!state.writing && !state.corked && !state.finished && !state.bufferProcessing && state.bufferedRequest) clearBuffer(this, state);
  }
};

Writable.prototype.setDefaultEncoding = function setDefaultEncoding(encoding) {
  // node::ParseEncoding() requires lower case.
  if (typeof encoding === 'string') encoding = encoding.toLowerCase();
  if (!(['hex', 'utf8', 'utf-8', 'ascii', 'binary', 'base64', 'ucs2', 'ucs-2', 'utf16le', 'utf-16le', 'raw'].indexOf((encoding + '').toLowerCase()) > -1)) throw new TypeError('Unknown encoding: ' + encoding);
  this._writableState.defaultEncoding = encoding;
  return this;
};

function decodeChunk(state, chunk, encoding) {
  if (!state.objectMode && state.decodeStrings !== false && typeof chunk === 'string') {
    chunk = Buffer.from(chunk, encoding);
  }
  return chunk;
}

Object.defineProperty(Writable.prototype, 'writableHighWaterMark', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function () {
    return this._writableState.highWaterMark;
  }
});

// if we're already writing something, then just put this
// in the queue, and wait our turn.  Otherwise, call _write
// If we return false, then we need a drain event, so set that flag.
function writeOrBuffer(stream, state, isBuf, chunk, encoding, cb) {
  if (!isBuf) {
    var newChunk = decodeChunk(state, chunk, encoding);
    if (chunk !== newChunk) {
      isBuf = true;
      encoding = 'buffer';
      chunk = newChunk;
    }
  }
  var len = state.objectMode ? 1 : chunk.length;

  state.length += len;

  var ret = state.length < state.highWaterMark;
  // we must ensure that previous needDrain will not be reset to false.
  if (!ret) state.needDrain = true;

  if (state.writing || state.corked) {
    var last = state.lastBufferedRequest;
    state.lastBufferedRequest = {
      chunk: chunk,
      encoding: encoding,
      isBuf: isBuf,
      callback: cb,
      next: null
    };
    if (last) {
      last.next = state.lastBufferedRequest;
    } else {
      state.bufferedRequest = state.lastBufferedRequest;
    }
    state.bufferedRequestCount += 1;
  } else {
    doWrite(stream, state, false, len, chunk, encoding, cb);
  }

  return ret;
}

function doWrite(stream, state, writev, len, chunk, encoding, cb) {
  state.writelen = len;
  state.writecb = cb;
  state.writing = true;
  state.sync = true;
  if (writev) stream._writev(chunk, state.onwrite);else stream._write(chunk, encoding, state.onwrite);
  state.sync = false;
}

function onwriteError(stream, state, sync, er, cb) {
  --state.pendingcb;

  if (sync) {
    // defer the callback if we are being called synchronously
    // to avoid piling up things on the stack
    pna.nextTick(cb, er);
    // this can emit finish, and it will always happen
    // after error
    pna.nextTick(finishMaybe, stream, state);
    stream._writableState.errorEmitted = true;
    stream.emit('error', er);
  } else {
    // the caller expect this to happen before if
    // it is async
    cb(er);
    stream._writableState.errorEmitted = true;
    stream.emit('error', er);
    // this can emit finish, but finish must
    // always follow error
    finishMaybe(stream, state);
  }
}

function onwriteStateUpdate(state) {
  state.writing = false;
  state.writecb = null;
  state.length -= state.writelen;
  state.writelen = 0;
}

function onwrite(stream, er) {
  var state = stream._writableState;
  var sync = state.sync;
  var cb = state.writecb;

  onwriteStateUpdate(state);

  if (er) onwriteError(stream, state, sync, er, cb);else {
    // Check if we're actually ready to finish, but don't emit yet
    var finished = needFinish(state);

    if (!finished && !state.corked && !state.bufferProcessing && state.bufferedRequest) {
      clearBuffer(stream, state);
    }

    if (sync) {
      /*<replacement>*/
      asyncWrite(afterWrite, stream, state, finished, cb);
      /*</replacement>*/
    } else {
      afterWrite(stream, state, finished, cb);
    }
  }
}

function afterWrite(stream, state, finished, cb) {
  if (!finished) onwriteDrain(stream, state);
  state.pendingcb--;
  cb();
  finishMaybe(stream, state);
}

// Must force callback to be called on nextTick, so that we don't
// emit 'drain' before the write() consumer gets the 'false' return
// value, and has a chance to attach a 'drain' listener.
function onwriteDrain(stream, state) {
  if (state.length === 0 && state.needDrain) {
    state.needDrain = false;
    stream.emit('drain');
  }
}

// if there's something in the buffer waiting, then process it
function clearBuffer(stream, state) {
  state.bufferProcessing = true;
  var entry = state.bufferedRequest;

  if (stream._writev && entry && entry.next) {
    // Fast case, write everything using _writev()
    var l = state.bufferedRequestCount;
    var buffer = new Array(l);
    var holder = state.corkedRequestsFree;
    holder.entry = entry;

    var count = 0;
    var allBuffers = true;
    while (entry) {
      buffer[count] = entry;
      if (!entry.isBuf) allBuffers = false;
      entry = entry.next;
      count += 1;
    }
    buffer.allBuffers = allBuffers;

    doWrite(stream, state, true, state.length, buffer, '', holder.finish);

    // doWrite is almost always async, defer these to save a bit of time
    // as the hot path ends with doWrite
    state.pendingcb++;
    state.lastBufferedRequest = null;
    if (holder.next) {
      state.corkedRequestsFree = holder.next;
      holder.next = null;
    } else {
      state.corkedRequestsFree = new CorkedRequest(state);
    }
    state.bufferedRequestCount = 0;
  } else {
    // Slow case, write chunks one-by-one
    while (entry) {
      var chunk = entry.chunk;
      var encoding = entry.encoding;
      var cb = entry.callback;
      var len = state.objectMode ? 1 : chunk.length;

      doWrite(stream, state, false, len, chunk, encoding, cb);
      entry = entry.next;
      state.bufferedRequestCount--;
      // if we didn't call the onwrite immediately, then
      // it means that we need to wait until it does.
      // also, that means that the chunk and cb are currently
      // being processed, so move the buffer counter past them.
      if (state.writing) {
        break;
      }
    }

    if (entry === null) state.lastBufferedRequest = null;
  }

  state.bufferedRequest = entry;
  state.bufferProcessing = false;
}

Writable.prototype._write = function (chunk, encoding, cb) {
  cb(new Error('_write() is not implemented'));
};

Writable.prototype._writev = null;

Writable.prototype.end = function (chunk, encoding, cb) {
  var state = this._writableState;

  if (typeof chunk === 'function') {
    cb = chunk;
    chunk = null;
    encoding = null;
  } else if (typeof encoding === 'function') {
    cb = encoding;
    encoding = null;
  }

  if (chunk !== null && chunk !== undefined) this.write(chunk, encoding);

  // .end() fully uncorks
  if (state.corked) {
    state.corked = 1;
    this.uncork();
  }

  // ignore unnecessary end() calls.
  if (!state.ending && !state.finished) endWritable(this, state, cb);
};

function needFinish(state) {
  return state.ending && state.length === 0 && state.bufferedRequest === null && !state.finished && !state.writing;
}
function callFinal(stream, state) {
  stream._final(function (err) {
    state.pendingcb--;
    if (err) {
      stream.emit('error', err);
    }
    state.prefinished = true;
    stream.emit('prefinish');
    finishMaybe(stream, state);
  });
}
function prefinish(stream, state) {
  if (!state.prefinished && !state.finalCalled) {
    if (typeof stream._final === 'function') {
      state.pendingcb++;
      state.finalCalled = true;
      pna.nextTick(callFinal, stream, state);
    } else {
      state.prefinished = true;
      stream.emit('prefinish');
    }
  }
}

function finishMaybe(stream, state) {
  var need = needFinish(state);
  if (need) {
    prefinish(stream, state);
    if (state.pendingcb === 0) {
      state.finished = true;
      stream.emit('finish');
    }
  }
  return need;
}

function endWritable(stream, state, cb) {
  state.ending = true;
  finishMaybe(stream, state);
  if (cb) {
    if (state.finished) pna.nextTick(cb);else stream.once('finish', cb);
  }
  state.ended = true;
  stream.writable = false;
}

function onCorkedFinish(corkReq, state, err) {
  var entry = corkReq.entry;
  corkReq.entry = null;
  while (entry) {
    var cb = entry.callback;
    state.pendingcb--;
    cb(err);
    entry = entry.next;
  }
  if (state.corkedRequestsFree) {
    state.corkedRequestsFree.next = corkReq;
  } else {
    state.corkedRequestsFree = corkReq;
  }
}

Object.defineProperty(Writable.prototype, 'destroyed', {
  get: function () {
    if (this._writableState === undefined) {
      return false;
    }
    return this._writableState.destroyed;
  },
  set: function (value) {
    // we ignore the value if the stream
    // has not been initialized yet
    if (!this._writableState) {
      return;
    }

    // backward compatibility, the user is explicitly
    // managing destroyed
    this._writableState.destroyed = value;
  }
});

Writable.prototype.destroy = destroyImpl.destroy;
Writable.prototype._undestroy = destroyImpl.undestroy;
Writable.prototype._destroy = function (err, cb) {
  this.end();
  cb(err);
};
/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(47), __webpack_require__(24)))

/***/ }),
/* 166 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(process) {// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

var getOwnPropertyDescriptors = Object.getOwnPropertyDescriptors ||
  function getOwnPropertyDescriptors(obj) {
    var keys = Object.keys(obj);
    var descriptors = {};
    for (var i = 0; i < keys.length; i++) {
      descriptors[keys[i]] = Object.getOwnPropertyDescriptor(obj, keys[i]);
    }
    return descriptors;
  };

var formatRegExp = /%[sdj%]/g;
exports.format = function(f) {
  if (!isString(f)) {
    var objects = [];
    for (var i = 0; i < arguments.length; i++) {
      objects.push(inspect(arguments[i]));
    }
    return objects.join(' ');
  }

  var i = 1;
  var args = arguments;
  var len = args.length;
  var str = String(f).replace(formatRegExp, function(x) {
    if (x === '%%') return '%';
    if (i >= len) return x;
    switch (x) {
      case '%s': return String(args[i++]);
      case '%d': return Number(args[i++]);
      case '%j':
        try {
          return JSON.stringify(args[i++]);
        } catch (_) {
          return '[Circular]';
        }
      default:
        return x;
    }
  });
  for (var x = args[i]; i < len; x = args[++i]) {
    if (isNull(x) || !isObject(x)) {
      str += ' ' + x;
    } else {
      str += ' ' + inspect(x);
    }
  }
  return str;
};


// Mark that a method should not be used.
// Returns a modified function which warns once by default.
// If --no-deprecation is set, then it is a no-op.
exports.deprecate = function(fn, msg) {
  if (typeof process !== 'undefined' && process.noDeprecation === true) {
    return fn;
  }

  // Allow for deprecating things in the process of starting up.
  if (typeof process === 'undefined') {
    return function() {
      return exports.deprecate(fn, msg).apply(this, arguments);
    };
  }

  var warned = false;
  function deprecated() {
    if (!warned) {
      if (process.throwDeprecation) {
        throw new Error(msg);
      } else if (process.traceDeprecation) {
        console.trace(msg);
      } else {
        console.error(msg);
      }
      warned = true;
    }
    return fn.apply(this, arguments);
  }

  return deprecated;
};


var debugs = {};
var debugEnviron;
exports.debuglog = function(set) {
  if (isUndefined(debugEnviron))
    debugEnviron = process.env.NODE_DEBUG || '';
  set = set.toUpperCase();
  if (!debugs[set]) {
    if (new RegExp('\\b' + set + '\\b', 'i').test(debugEnviron)) {
      var pid = process.pid;
      debugs[set] = function() {
        var msg = exports.format.apply(exports, arguments);
        console.error('%s %d: %s', set, pid, msg);
      };
    } else {
      debugs[set] = function() {};
    }
  }
  return debugs[set];
};


/**
 * Echos the value of a value. Trys to print the value out
 * in the best way possible given the different types.
 *
 * @param {Object} obj The object to print out.
 * @param {Object} opts Optional options object that alters the output.
 */
/* legacy: obj, showHidden, depth, colors*/
function inspect(obj, opts) {
  // default options
  var ctx = {
    seen: [],
    stylize: stylizeNoColor
  };
  // legacy...
  if (arguments.length >= 3) ctx.depth = arguments[2];
  if (arguments.length >= 4) ctx.colors = arguments[3];
  if (isBoolean(opts)) {
    // legacy...
    ctx.showHidden = opts;
  } else if (opts) {
    // got an "options" object
    exports._extend(ctx, opts);
  }
  // set default options
  if (isUndefined(ctx.showHidden)) ctx.showHidden = false;
  if (isUndefined(ctx.depth)) ctx.depth = 2;
  if (isUndefined(ctx.colors)) ctx.colors = false;
  if (isUndefined(ctx.customInspect)) ctx.customInspect = true;
  if (ctx.colors) ctx.stylize = stylizeWithColor;
  return formatValue(ctx, obj, ctx.depth);
}
exports.inspect = inspect;


// http://en.wikipedia.org/wiki/ANSI_escape_code#graphics
inspect.colors = {
  'bold' : [1, 22],
  'italic' : [3, 23],
  'underline' : [4, 24],
  'inverse' : [7, 27],
  'white' : [37, 39],
  'grey' : [90, 39],
  'black' : [30, 39],
  'blue' : [34, 39],
  'cyan' : [36, 39],
  'green' : [32, 39],
  'magenta' : [35, 39],
  'red' : [31, 39],
  'yellow' : [33, 39]
};

// Don't use 'blue' not visible on cmd.exe
inspect.styles = {
  'special': 'cyan',
  'number': 'yellow',
  'boolean': 'yellow',
  'undefined': 'grey',
  'null': 'bold',
  'string': 'green',
  'date': 'magenta',
  // "name": intentionally not styling
  'regexp': 'red'
};


function stylizeWithColor(str, styleType) {
  var style = inspect.styles[styleType];

  if (style) {
    return '\u001b[' + inspect.colors[style][0] + 'm' + str +
           '\u001b[' + inspect.colors[style][1] + 'm';
  } else {
    return str;
  }
}


function stylizeNoColor(str, styleType) {
  return str;
}


function arrayToHash(array) {
  var hash = {};

  array.forEach(function(val, idx) {
    hash[val] = true;
  });

  return hash;
}


function formatValue(ctx, value, recurseTimes) {
  // Provide a hook for user-specified inspect functions.
  // Check that value is an object with an inspect function on it
  if (ctx.customInspect &&
      value &&
      isFunction(value.inspect) &&
      // Filter out the util module, it's inspect function is special
      value.inspect !== exports.inspect &&
      // Also filter out any prototype objects using the circular check.
      !(value.constructor && value.constructor.prototype === value)) {
    var ret = value.inspect(recurseTimes, ctx);
    if (!isString(ret)) {
      ret = formatValue(ctx, ret, recurseTimes);
    }
    return ret;
  }

  // Primitive types cannot have properties
  var primitive = formatPrimitive(ctx, value);
  if (primitive) {
    return primitive;
  }

  // Look up the keys of the object.
  var keys = Object.keys(value);
  var visibleKeys = arrayToHash(keys);

  if (ctx.showHidden) {
    keys = Object.getOwnPropertyNames(value);
  }

  // IE doesn't make error fields non-enumerable
  // http://msdn.microsoft.com/en-us/library/ie/dww52sbt(v=vs.94).aspx
  if (isError(value)
      && (keys.indexOf('message') >= 0 || keys.indexOf('description') >= 0)) {
    return formatError(value);
  }

  // Some type of object without properties can be shortcutted.
  if (keys.length === 0) {
    if (isFunction(value)) {
      var name = value.name ? ': ' + value.name : '';
      return ctx.stylize('[Function' + name + ']', 'special');
    }
    if (isRegExp(value)) {
      return ctx.stylize(RegExp.prototype.toString.call(value), 'regexp');
    }
    if (isDate(value)) {
      return ctx.stylize(Date.prototype.toString.call(value), 'date');
    }
    if (isError(value)) {
      return formatError(value);
    }
  }

  var base = '', array = false, braces = ['{', '}'];

  // Make Array say that they are Array
  if (isArray(value)) {
    array = true;
    braces = ['[', ']'];
  }

  // Make functions say that they are functions
  if (isFunction(value)) {
    var n = value.name ? ': ' + value.name : '';
    base = ' [Function' + n + ']';
  }

  // Make RegExps say that they are RegExps
  if (isRegExp(value)) {
    base = ' ' + RegExp.prototype.toString.call(value);
  }

  // Make dates with properties first say the date
  if (isDate(value)) {
    base = ' ' + Date.prototype.toUTCString.call(value);
  }

  // Make error with message first say the error
  if (isError(value)) {
    base = ' ' + formatError(value);
  }

  if (keys.length === 0 && (!array || value.length == 0)) {
    return braces[0] + base + braces[1];
  }

  if (recurseTimes < 0) {
    if (isRegExp(value)) {
      return ctx.stylize(RegExp.prototype.toString.call(value), 'regexp');
    } else {
      return ctx.stylize('[Object]', 'special');
    }
  }

  ctx.seen.push(value);

  var output;
  if (array) {
    output = formatArray(ctx, value, recurseTimes, visibleKeys, keys);
  } else {
    output = keys.map(function(key) {
      return formatProperty(ctx, value, recurseTimes, visibleKeys, key, array);
    });
  }

  ctx.seen.pop();

  return reduceToSingleString(output, base, braces);
}


function formatPrimitive(ctx, value) {
  if (isUndefined(value))
    return ctx.stylize('undefined', 'undefined');
  if (isString(value)) {
    var simple = '\'' + JSON.stringify(value).replace(/^"|"$/g, '')
                                             .replace(/'/g, "\\'")
                                             .replace(/\\"/g, '"') + '\'';
    return ctx.stylize(simple, 'string');
  }
  if (isNumber(value))
    return ctx.stylize('' + value, 'number');
  if (isBoolean(value))
    return ctx.stylize('' + value, 'boolean');
  // For some reason typeof null is "object", so special case here.
  if (isNull(value))
    return ctx.stylize('null', 'null');
}


function formatError(value) {
  return '[' + Error.prototype.toString.call(value) + ']';
}


function formatArray(ctx, value, recurseTimes, visibleKeys, keys) {
  var output = [];
  for (var i = 0, l = value.length; i < l; ++i) {
    if (hasOwnProperty(value, String(i))) {
      output.push(formatProperty(ctx, value, recurseTimes, visibleKeys,
          String(i), true));
    } else {
      output.push('');
    }
  }
  keys.forEach(function(key) {
    if (!key.match(/^\d+$/)) {
      output.push(formatProperty(ctx, value, recurseTimes, visibleKeys,
          key, true));
    }
  });
  return output;
}


function formatProperty(ctx, value, recurseTimes, visibleKeys, key, array) {
  var name, str, desc;
  desc = Object.getOwnPropertyDescriptor(value, key) || { value: value[key] };
  if (desc.get) {
    if (desc.set) {
      str = ctx.stylize('[Getter/Setter]', 'special');
    } else {
      str = ctx.stylize('[Getter]', 'special');
    }
  } else {
    if (desc.set) {
      str = ctx.stylize('[Setter]', 'special');
    }
  }
  if (!hasOwnProperty(visibleKeys, key)) {
    name = '[' + key + ']';
  }
  if (!str) {
    if (ctx.seen.indexOf(desc.value) < 0) {
      if (isNull(recurseTimes)) {
        str = formatValue(ctx, desc.value, null);
      } else {
        str = formatValue(ctx, desc.value, recurseTimes - 1);
      }
      if (str.indexOf('\n') > -1) {
        if (array) {
          str = str.split('\n').map(function(line) {
            return '  ' + line;
          }).join('\n').substr(2);
        } else {
          str = '\n' + str.split('\n').map(function(line) {
            return '   ' + line;
          }).join('\n');
        }
      }
    } else {
      str = ctx.stylize('[Circular]', 'special');
    }
  }
  if (isUndefined(name)) {
    if (array && key.match(/^\d+$/)) {
      return str;
    }
    name = JSON.stringify('' + key);
    if (name.match(/^"([a-zA-Z_][a-zA-Z_0-9]*)"$/)) {
      name = name.substr(1, name.length - 2);
      name = ctx.stylize(name, 'name');
    } else {
      name = name.replace(/'/g, "\\'")
                 .replace(/\\"/g, '"')
                 .replace(/(^"|"$)/g, "'");
      name = ctx.stylize(name, 'string');
    }
  }

  return name + ': ' + str;
}


function reduceToSingleString(output, base, braces) {
  var numLinesEst = 0;
  var length = output.reduce(function(prev, cur) {
    numLinesEst++;
    if (cur.indexOf('\n') >= 0) numLinesEst++;
    return prev + cur.replace(/\u001b\[\d\d?m/g, '').length + 1;
  }, 0);

  if (length > 60) {
    return braces[0] +
           (base === '' ? '' : base + '\n ') +
           ' ' +
           output.join(',\n  ') +
           ' ' +
           braces[1];
  }

  return braces[0] + base + ' ' + output.join(', ') + ' ' + braces[1];
}


// NOTE: These type checking functions intentionally don't use `instanceof`
// because it is fragile and can be easily faked with `Object.create()`.
function isArray(ar) {
  return Array.isArray(ar);
}
exports.isArray = isArray;

function isBoolean(arg) {
  return typeof arg === 'boolean';
}
exports.isBoolean = isBoolean;

function isNull(arg) {
  return arg === null;
}
exports.isNull = isNull;

function isNullOrUndefined(arg) {
  return arg == null;
}
exports.isNullOrUndefined = isNullOrUndefined;

function isNumber(arg) {
  return typeof arg === 'number';
}
exports.isNumber = isNumber;

function isString(arg) {
  return typeof arg === 'string';
}
exports.isString = isString;

function isSymbol(arg) {
  return typeof arg === 'symbol';
}
exports.isSymbol = isSymbol;

function isUndefined(arg) {
  return arg === void 0;
}
exports.isUndefined = isUndefined;

function isRegExp(re) {
  return isObject(re) && objectToString(re) === '[object RegExp]';
}
exports.isRegExp = isRegExp;

function isObject(arg) {
  return typeof arg === 'object' && arg !== null;
}
exports.isObject = isObject;

function isDate(d) {
  return isObject(d) && objectToString(d) === '[object Date]';
}
exports.isDate = isDate;

function isError(e) {
  return isObject(e) &&
      (objectToString(e) === '[object Error]' || e instanceof Error);
}
exports.isError = isError;

function isFunction(arg) {
  return typeof arg === 'function';
}
exports.isFunction = isFunction;

function isPrimitive(arg) {
  return arg === null ||
         typeof arg === 'boolean' ||
         typeof arg === 'number' ||
         typeof arg === 'string' ||
         typeof arg === 'symbol' ||  // ES6 symbol
         typeof arg === 'undefined';
}
exports.isPrimitive = isPrimitive;

exports.isBuffer = __webpack_require__(352);

function objectToString(o) {
  return Object.prototype.toString.call(o);
}


function pad(n) {
  return n < 10 ? '0' + n.toString(10) : n.toString(10);
}


var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep',
              'Oct', 'Nov', 'Dec'];

// 26 Feb 16:19:34
function timestamp() {
  var d = new Date();
  var time = [pad(d.getHours()),
              pad(d.getMinutes()),
              pad(d.getSeconds())].join(':');
  return [d.getDate(), months[d.getMonth()], time].join(' ');
}


// log is just a thin wrapper to console.log that prepends a timestamp
exports.log = function() {
  console.log('%s - %s', timestamp(), exports.format.apply(exports, arguments));
};


/**
 * Inherit the prototype methods from one constructor into another.
 *
 * The Function.prototype.inherits from lang.js rewritten as a standalone
 * function (not on Function.prototype). NOTE: If this file is to be loaded
 * during bootstrapping this function needs to be rewritten using some native
 * functions as prototype setup using normal JavaScript does not work as
 * expected during bootstrapping (see mirror.js in r114903).
 *
 * @param {function} ctor Constructor function which needs to inherit the
 *     prototype.
 * @param {function} superCtor Constructor function to inherit prototype from.
 */
exports.inherits = __webpack_require__(353);

exports._extend = function(origin, add) {
  // Don't do anything if add isn't an object
  if (!add || !isObject(add)) return origin;

  var keys = Object.keys(add);
  var i = keys.length;
  while (i--) {
    origin[keys[i]] = add[keys[i]];
  }
  return origin;
};

function hasOwnProperty(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

var kCustomPromisifiedSymbol = typeof Symbol !== 'undefined' ? Symbol('util.promisify.custom') : undefined;

exports.promisify = function promisify(original) {
  if (typeof original !== 'function')
    throw new TypeError('The "original" argument must be of type Function');

  if (kCustomPromisifiedSymbol && original[kCustomPromisifiedSymbol]) {
    var fn = original[kCustomPromisifiedSymbol];
    if (typeof fn !== 'function') {
      throw new TypeError('The "util.promisify.custom" argument must be of type Function');
    }
    Object.defineProperty(fn, kCustomPromisifiedSymbol, {
      value: fn, enumerable: false, writable: false, configurable: true
    });
    return fn;
  }

  function fn() {
    var promiseResolve, promiseReject;
    var promise = new Promise(function (resolve, reject) {
      promiseResolve = resolve;
      promiseReject = reject;
    });

    var args = [];
    for (var i = 0; i < arguments.length; i++) {
      args.push(arguments[i]);
    }
    args.push(function (err, value) {
      if (err) {
        promiseReject(err);
      } else {
        promiseResolve(value);
      }
    });

    try {
      original.apply(this, args);
    } catch (err) {
      promiseReject(err);
    }

    return promise;
  }

  Object.setPrototypeOf(fn, Object.getPrototypeOf(original));

  if (kCustomPromisifiedSymbol) Object.defineProperty(fn, kCustomPromisifiedSymbol, {
    value: fn, enumerable: false, writable: false, configurable: true
  });
  return Object.defineProperties(
    fn,
    getOwnPropertyDescriptors(original)
  );
}

exports.promisify.custom = kCustomPromisifiedSymbol

function callbackifyOnRejected(reason, cb) {
  // `!reason` guard inspired by bluebird (Ref: https://goo.gl/t5IS6M).
  // Because `null` is a special error value in callbacks which means "no error
  // occurred", we error-wrap so the callback consumer can distinguish between
  // "the promise rejected with null" or "the promise fulfilled with undefined".
  if (!reason) {
    var newReason = new Error('Promise was rejected with a falsy value');
    newReason.reason = reason;
    reason = newReason;
  }
  return cb(reason);
}

function callbackify(original) {
  if (typeof original !== 'function') {
    throw new TypeError('The "original" argument must be of type Function');
  }

  // We DO NOT return the promise as it gives the user a false sense that
  // the promise is actually somehow related to the callback's execution
  // and that the callback throwing will reject the promise.
  function callbackified() {
    var args = [];
    for (var i = 0; i < arguments.length; i++) {
      args.push(arguments[i]);
    }

    var maybeCb = args.pop();
    if (typeof maybeCb !== 'function') {
      throw new TypeError('The last argument must be of type Function');
    }
    var self = this;
    var cb = function() {
      return maybeCb.apply(self, arguments);
    };
    // In true node style we process the callback on `nextTick` with all the
    // implications (stack, `uncaughtException`, `async_hooks`)
    original.apply(this, args)
      .then(function(ret) { process.nextTick(cb, null, ret) },
            function(rej) { process.nextTick(callbackifyOnRejected, rej, cb) });
  }

  Object.setPrototypeOf(callbackified, Object.getPrototypeOf(original));
  Object.defineProperties(callbackified,
                          getOwnPropertyDescriptors(original));
  return callbackified;
}
exports.callbackify = callbackify;

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(47)))

/***/ }),
/* 167 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var WordArray = C_lib.WordArray;
	    var Hasher = C_lib.Hasher;
	    var C_algo = C.algo;

	    // Reusable object
	    var W = [];

	    /**
	     * SHA-1 hash algorithm.
	     */
	    var SHA1 = C_algo.SHA1 = Hasher.extend({
	        _doReset: function () {
	            this._hash = new WordArray.init([
	                0x67452301, 0xefcdab89,
	                0x98badcfe, 0x10325476,
	                0xc3d2e1f0
	            ]);
	        },

	        _doProcessBlock: function (M, offset) {
	            // Shortcut
	            var H = this._hash.words;

	            // Working variables
	            var a = H[0];
	            var b = H[1];
	            var c = H[2];
	            var d = H[3];
	            var e = H[4];

	            // Computation
	            for (var i = 0; i < 80; i++) {
	                if (i < 16) {
	                    W[i] = M[offset + i] | 0;
	                } else {
	                    var n = W[i - 3] ^ W[i - 8] ^ W[i - 14] ^ W[i - 16];
	                    W[i] = (n << 1) | (n >>> 31);
	                }

	                var t = ((a << 5) | (a >>> 27)) + e + W[i];
	                if (i < 20) {
	                    t += ((b & c) | (~b & d)) + 0x5a827999;
	                } else if (i < 40) {
	                    t += (b ^ c ^ d) + 0x6ed9eba1;
	                } else if (i < 60) {
	                    t += ((b & c) | (b & d) | (c & d)) - 0x70e44324;
	                } else /* if (i < 80) */ {
	                    t += (b ^ c ^ d) - 0x359d3e2a;
	                }

	                e = d;
	                d = c;
	                c = (b << 30) | (b >>> 2);
	                b = a;
	                a = t;
	            }

	            // Intermediate hash value
	            H[0] = (H[0] + a) | 0;
	            H[1] = (H[1] + b) | 0;
	            H[2] = (H[2] + c) | 0;
	            H[3] = (H[3] + d) | 0;
	            H[4] = (H[4] + e) | 0;
	        },

	        _doFinalize: function () {
	            // Shortcuts
	            var data = this._data;
	            var dataWords = data.words;

	            var nBitsTotal = this._nDataBytes * 8;
	            var nBitsLeft = data.sigBytes * 8;

	            // Add padding
	            dataWords[nBitsLeft >>> 5] |= 0x80 << (24 - nBitsLeft % 32);
	            dataWords[(((nBitsLeft + 64) >>> 9) << 4) + 14] = Math.floor(nBitsTotal / 0x100000000);
	            dataWords[(((nBitsLeft + 64) >>> 9) << 4) + 15] = nBitsTotal;
	            data.sigBytes = dataWords.length * 4;

	            // Hash final blocks
	            this._process();

	            // Return final computed hash
	            return this._hash;
	        },

	        clone: function () {
	            var clone = Hasher.clone.call(this);
	            clone._hash = this._hash.clone();

	            return clone;
	        }
	    });

	    /**
	     * Shortcut function to the hasher's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     *
	     * @return {WordArray} The hash.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hash = CryptoJS.SHA1('message');
	     *     var hash = CryptoJS.SHA1(wordArray);
	     */
	    C.SHA1 = Hasher._createHelper(SHA1);

	    /**
	     * Shortcut function to the HMAC's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     * @param {WordArray|string} key The secret key.
	     *
	     * @return {WordArray} The HMAC.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hmac = CryptoJS.HmacSHA1(message, key);
	     */
	    C.HmacSHA1 = Hasher._createHmacHelper(SHA1);
	}());


	return CryptoJS.SHA1;

}));

/***/ }),
/* 168 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var Base = C_lib.Base;
	    var C_enc = C.enc;
	    var Utf8 = C_enc.Utf8;
	    var C_algo = C.algo;

	    /**
	     * HMAC algorithm.
	     */
	    var HMAC = C_algo.HMAC = Base.extend({
	        /**
	         * Initializes a newly created HMAC.
	         *
	         * @param {Hasher} hasher The hash algorithm to use.
	         * @param {WordArray|string} key The secret key.
	         *
	         * @example
	         *
	         *     var hmacHasher = CryptoJS.algo.HMAC.create(CryptoJS.algo.SHA256, key);
	         */
	        init: function (hasher, key) {
	            // Init hasher
	            hasher = this._hasher = new hasher.init();

	            // Convert string to WordArray, else assume WordArray already
	            if (typeof key == 'string') {
	                key = Utf8.parse(key);
	            }

	            // Shortcuts
	            var hasherBlockSize = hasher.blockSize;
	            var hasherBlockSizeBytes = hasherBlockSize * 4;

	            // Allow arbitrary length keys
	            if (key.sigBytes > hasherBlockSizeBytes) {
	                key = hasher.finalize(key);
	            }

	            // Clamp excess bits
	            key.clamp();

	            // Clone key for inner and outer pads
	            var oKey = this._oKey = key.clone();
	            var iKey = this._iKey = key.clone();

	            // Shortcuts
	            var oKeyWords = oKey.words;
	            var iKeyWords = iKey.words;

	            // XOR keys with pad constants
	            for (var i = 0; i < hasherBlockSize; i++) {
	                oKeyWords[i] ^= 0x5c5c5c5c;
	                iKeyWords[i] ^= 0x36363636;
	            }
	            oKey.sigBytes = iKey.sigBytes = hasherBlockSizeBytes;

	            // Set initial values
	            this.reset();
	        },

	        /**
	         * Resets this HMAC to its initial state.
	         *
	         * @example
	         *
	         *     hmacHasher.reset();
	         */
	        reset: function () {
	            // Shortcut
	            var hasher = this._hasher;

	            // Reset
	            hasher.reset();
	            hasher.update(this._iKey);
	        },

	        /**
	         * Updates this HMAC with a message.
	         *
	         * @param {WordArray|string} messageUpdate The message to append.
	         *
	         * @return {HMAC} This HMAC instance.
	         *
	         * @example
	         *
	         *     hmacHasher.update('message');
	         *     hmacHasher.update(wordArray);
	         */
	        update: function (messageUpdate) {
	            this._hasher.update(messageUpdate);

	            // Chainable
	            return this;
	        },

	        /**
	         * Finalizes the HMAC computation.
	         * Note that the finalize operation is effectively a destructive, read-once operation.
	         *
	         * @param {WordArray|string} messageUpdate (Optional) A final message update.
	         *
	         * @return {WordArray} The HMAC.
	         *
	         * @example
	         *
	         *     var hmac = hmacHasher.finalize();
	         *     var hmac = hmacHasher.finalize('message');
	         *     var hmac = hmacHasher.finalize(wordArray);
	         */
	        finalize: function (messageUpdate) {
	            // Shortcut
	            var hasher = this._hasher;

	            // Compute HMAC
	            var innerHash = hasher.finalize(messageUpdate);
	            hasher.reset();
	            var hmac = hasher.finalize(this._oKey.clone().concat(innerHash));

	            return hmac;
	        }
	    });
	}());


}));

/***/ }),
/* 169 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(Buffer) {// Generated by CoffeeScript 1.7.1
(function() {
  var DecodeStream, iconv;

  try {
    iconv = __webpack_require__(170);
  } catch (_error) {}

  DecodeStream = (function() {
    var key;

    function DecodeStream(buffer) {
      this.buffer = buffer;
      this.pos = 0;
      this.length = this.buffer.length;
    }

    DecodeStream.TYPES = {
      UInt8: 1,
      UInt16: 2,
      UInt24: 3,
      UInt32: 4,
      Int8: 1,
      Int16: 2,
      Int24: 3,
      Int32: 4,
      Float: 4,
      Double: 8
    };

    for (key in Buffer.prototype) {
      if (key.slice(0, 4) === 'read') {
        (function(key) {
          var bytes;
          bytes = DecodeStream.TYPES[key.replace(/read|[BL]E/g, '')];
          return DecodeStream.prototype[key] = function() {
            var ret;
            ret = this.buffer[key](this.pos);
            this.pos += bytes;
            return ret;
          };
        })(key);
      }
    }

    DecodeStream.prototype.readString = function(length, encoding) {
      var buf, byte, i, _i, _ref;
      if (encoding == null) {
        encoding = 'ascii';
      }
      switch (encoding) {
        case 'utf16le':
        case 'ucs2':
        case 'utf8':
        case 'ascii':
          return this.buffer.toString(encoding, this.pos, this.pos += length);
        case 'utf16be':
          buf = new Buffer(this.readBuffer(length));
          for (i = _i = 0, _ref = buf.length - 1; _i < _ref; i = _i += 2) {
            byte = buf[i];
            buf[i] = buf[i + 1];
            buf[i + 1] = byte;
          }
          return buf.toString('utf16le');
        default:
          buf = this.readBuffer(length);
          if (iconv) {
            try {
              return iconv.decode(buf, encoding);
            } catch (_error) {}
          }
          return buf;
      }
    };

    DecodeStream.prototype.readBuffer = function(length) {
      return this.buffer.slice(this.pos, this.pos += length);
    };

    DecodeStream.prototype.readUInt24BE = function() {
      return (this.readUInt16BE() << 8) + this.readUInt8();
    };

    DecodeStream.prototype.readUInt24LE = function() {
      return this.readUInt16LE() + (this.readUInt8() << 16);
    };

    DecodeStream.prototype.readInt24BE = function() {
      return (this.readInt16BE() << 8) + this.readUInt8();
    };

    DecodeStream.prototype.readInt24LE = function() {
      return this.readUInt16LE() + (this.readInt8() << 16);
    };

    return DecodeStream;

  })();

  module.exports = DecodeStream;

}).call(this);

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer))

/***/ }),
/* 170 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var Buffer = __webpack_require__(39).Buffer;

var bomHandling = __webpack_require__(389),
    iconv = module.exports;

// All codecs and aliases are kept here, keyed by encoding name/alias.
// They are lazy loaded in `iconv.getCodec` from `encodings/index.js`.
iconv.encodings = null;

// Characters emitted in case of error.
iconv.defaultCharUnicode = '�';
iconv.defaultCharSingleByte = '?';

// Public API.
iconv.encode = function encode(str, encoding, options) {
    str = "" + (str || ""); // Ensure string.

    var encoder = iconv.getEncoder(encoding, options);

    var res = encoder.write(str);
    var trail = encoder.end();
    
    return (trail && trail.length > 0) ? Buffer.concat([res, trail]) : res;
}

iconv.decode = function decode(buf, encoding, options) {
    if (typeof buf === 'string') {
        if (!iconv.skipDecodeWarning) {
            console.error('Iconv-lite warning: decode()-ing strings is deprecated. Refer to https://github.com/ashtuchkin/iconv-lite/wiki/Use-Buffers-when-decoding');
            iconv.skipDecodeWarning = true;
        }

        buf = Buffer.from("" + (buf || ""), "binary"); // Ensure buffer.
    }

    var decoder = iconv.getDecoder(encoding, options);

    var res = decoder.write(buf);
    var trail = decoder.end();

    return trail ? (res + trail) : res;
}

iconv.encodingExists = function encodingExists(enc) {
    try {
        iconv.getCodec(enc);
        return true;
    } catch (e) {
        return false;
    }
}

// Legacy aliases to convert functions
iconv.toEncoding = iconv.encode;
iconv.fromEncoding = iconv.decode;

// Search for a codec in iconv.encodings. Cache codec data in iconv._codecDataCache.
iconv._codecDataCache = {};
iconv.getCodec = function getCodec(encoding) {
    if (!iconv.encodings)
        iconv.encodings = __webpack_require__(390); // Lazy load all encoding definitions.
    
    // Canonicalize encoding name: strip all non-alphanumeric chars and appended year.
    var enc = iconv._canonicalizeEncoding(encoding);

    // Traverse iconv.encodings to find actual codec.
    var codecOptions = {};
    while (true) {
        var codec = iconv._codecDataCache[enc];
        if (codec)
            return codec;

        var codecDef = iconv.encodings[enc];

        switch (typeof codecDef) {
            case "string": // Direct alias to other encoding.
                enc = codecDef;
                break;

            case "object": // Alias with options. Can be layered.
                for (var key in codecDef)
                    codecOptions[key] = codecDef[key];

                if (!codecOptions.encodingName)
                    codecOptions.encodingName = enc;
                
                enc = codecDef.type;
                break;

            case "function": // Codec itself.
                if (!codecOptions.encodingName)
                    codecOptions.encodingName = enc;

                // The codec function must load all tables and return object with .encoder and .decoder methods.
                // It'll be called only once (for each different options object).
                codec = new codecDef(codecOptions, iconv);

                iconv._codecDataCache[codecOptions.encodingName] = codec; // Save it to be reused later.
                return codec;

            default:
                throw new Error("Encoding not recognized: '" + encoding + "' (searched as: '"+enc+"')");
        }
    }
}

iconv._canonicalizeEncoding = function(encoding) {
    // Canonicalize encoding name: strip all non-alphanumeric chars and appended year.
    return (''+encoding).toLowerCase().replace(/:\d{4}$|[^0-9a-z]/g, "");
}

iconv.getEncoder = function getEncoder(encoding, options) {
    var codec = iconv.getCodec(encoding),
        encoder = new codec.encoder(options, codec);

    if (codec.bomAware && options && options.addBOM)
        encoder = new bomHandling.PrependBOM(encoder, options);

    return encoder;
}

iconv.getDecoder = function getDecoder(encoding, options) {
    var codec = iconv.getCodec(encoding),
        decoder = new codec.decoder(options, codec);

    if (codec.bomAware && !(options && options.stripBOM === false))
        decoder = new bomHandling.StripBOM(decoder, options);

    return decoder;
}

// Streaming API
// NOTE: Streaming API naturally depends on 'stream' module from Node.js. Unfortunately in browser environments this module can add
// up to 100Kb to the output bundle. To avoid unnecessary code bloat, we don't enable Streaming API in browser by default.
// If you would like to enable it explicitly, please add the following code to your app:
// > iconv.enableStreamingAPI(require('stream'));
iconv.enableStreamingAPI = function enableStreamingAPI(stream_module) {
    if (iconv.supportsStreams)
        return;

    // Dependency-inject stream module to create IconvLite stream classes.
    var streams = __webpack_require__(405)(stream_module);

    // Not public API yet, but expose the stream classes.
    iconv.IconvLiteEncoderStream = streams.IconvLiteEncoderStream;
    iconv.IconvLiteDecoderStream = streams.IconvLiteDecoderStream;

    // Streaming API.
    iconv.encodeStream = function encodeStream(encoding, options) {
        return new iconv.IconvLiteEncoderStream(iconv.getEncoder(encoding, options), options);
    }

    iconv.decodeStream = function decodeStream(encoding, options) {
        return new iconv.IconvLiteDecoderStream(iconv.getDecoder(encoding, options), options);
    }

    iconv.supportsStreams = true;
}

// Enable Streaming API automatically if 'stream' module is available and non-empty (the majority of environments).
var stream_module;
try {
    stream_module = __webpack_require__(406);
} catch (e) {}

if (stream_module && stream_module.Transform) {
    iconv.enableStreamingAPI(stream_module);

} else {
    // In rare cases where 'stream' module is not available by default, throw a helpful exception.
    iconv.encodeStream = iconv.decodeStream = function() {
        throw new Error("iconv-lite Streaming API is not enabled. Use iconv.enableStreamingAPI(require('stream')); to enable it.");
    };
}

if (false) {}


/***/ }),
/* 171 */
/***/ (function(module) {

module.exports = JSON.parse("[[\"0\",\"\\u0000\",127,\"€\"],[\"8140\",\"丂丄丅丆丏丒丗丟丠両丣並丩丮丯丱丳丵丷丼乀乁乂乄乆乊乑乕乗乚乛乢乣乤乥乧乨乪\",5,\"乲乴\",9,\"乿\",6,\"亇亊\"],[\"8180\",\"亐亖亗亙亜亝亞亣亪亯亰亱亴亶亷亸亹亼亽亾仈仌仏仐仒仚仛仜仠仢仦仧仩仭仮仯仱仴仸仹仺仼仾伀伂\",6,\"伋伌伒\",4,\"伜伝伡伣伨伩伬伭伮伱伳伵伷伹伻伾\",4,\"佄佅佇\",5,\"佒佔佖佡佢佦佨佪佫佭佮佱佲併佷佸佹佺佽侀侁侂侅來侇侊侌侎侐侒侓侕侖侘侙侚侜侞侟価侢\"],[\"8240\",\"侤侫侭侰\",4,\"侶\",8,\"俀俁係俆俇俈俉俋俌俍俒\",4,\"俙俛俠俢俤俥俧俫俬俰俲俴俵俶俷俹俻俼俽俿\",11],[\"8280\",\"個倎倐們倓倕倖倗倛倝倞倠倢倣値倧倫倯\",10,\"倻倽倿偀偁偂偄偅偆偉偊偋偍偐\",4,\"偖偗偘偙偛偝\",7,\"偦\",5,\"偭\",8,\"偸偹偺偼偽傁傂傃傄傆傇傉傊傋傌傎\",20,\"傤傦傪傫傭\",4,\"傳\",6,\"傼\"],[\"8340\",\"傽\",17,\"僐\",5,\"僗僘僙僛\",10,\"僨僩僪僫僯僰僱僲僴僶\",4,\"僼\",9,\"儈\"],[\"8380\",\"儉儊儌\",5,\"儓\",13,\"儢\",28,\"兂兇兊兌兎兏児兒兓兗兘兙兛兝\",4,\"兣兤兦內兩兪兯兲兺兾兿冃冄円冇冊冋冎冏冐冑冓冔冘冚冝冞冟冡冣冦\",4,\"冭冮冴冸冹冺冾冿凁凂凃凅凈凊凍凎凐凒\",5],[\"8440\",\"凘凙凚凜凞凟凢凣凥\",5,\"凬凮凱凲凴凷凾刄刅刉刋刌刏刐刓刔刕刜刞刟刡刢刣別刦刧刪刬刯刱刲刴刵刼刾剄\",5,\"剋剎剏剒剓剕剗剘\"],[\"8480\",\"剙剚剛剝剟剠剢剣剤剦剨剫剬剭剮剰剱剳\",9,\"剾劀劃\",4,\"劉\",6,\"劑劒劔\",6,\"劜劤劥劦劧劮劯劰労\",9,\"勀勁勂勄勅勆勈勊勌勍勎勏勑勓勔動勗務\",5,\"勠勡勢勣勥\",10,\"勱\",7,\"勻勼勽匁匂匃匄匇匉匊匋匌匎\"],[\"8540\",\"匑匒匓匔匘匛匜匞匟匢匤匥匧匨匩匫匬匭匯\",9,\"匼匽區卂卄卆卋卌卍卐協単卙卛卝卥卨卪卬卭卲卶卹卻卼卽卾厀厁厃厇厈厊厎厏\"],[\"8580\",\"厐\",4,\"厖厗厙厛厜厞厠厡厤厧厪厫厬厭厯\",6,\"厷厸厹厺厼厽厾叀參\",4,\"収叏叐叒叓叕叚叜叝叞叡叢叧叴叺叾叿吀吂吅吇吋吔吘吙吚吜吢吤吥吪吰吳吶吷吺吽吿呁呂呄呅呇呉呌呍呎呏呑呚呝\",4,\"呣呥呧呩\",7,\"呴呹呺呾呿咁咃咅咇咈咉咊咍咑咓咗咘咜咞咟咠咡\"],[\"8640\",\"咢咥咮咰咲咵咶咷咹咺咼咾哃哅哊哋哖哘哛哠\",4,\"哫哬哯哰哱哴\",5,\"哻哾唀唂唃唄唅唈唊\",4,\"唒唓唕\",5,\"唜唝唞唟唡唥唦\"],[\"8680\",\"唨唩唫唭唲唴唵唶唸唹唺唻唽啀啂啅啇啈啋\",4,\"啑啒啓啔啗\",4,\"啝啞啟啠啢啣啨啩啫啯\",5,\"啹啺啽啿喅喆喌喍喎喐喒喓喕喖喗喚喛喞喠\",6,\"喨\",8,\"喲喴営喸喺喼喿\",4,\"嗆嗇嗈嗊嗋嗎嗏嗐嗕嗗\",4,\"嗞嗠嗢嗧嗩嗭嗮嗰嗱嗴嗶嗸\",4,\"嗿嘂嘃嘄嘅\"],[\"8740\",\"嘆嘇嘊嘋嘍嘐\",7,\"嘙嘚嘜嘝嘠嘡嘢嘥嘦嘨嘩嘪嘫嘮嘯嘰嘳嘵嘷嘸嘺嘼嘽嘾噀\",11,\"噏\",4,\"噕噖噚噛噝\",4],[\"8780\",\"噣噥噦噧噭噮噯噰噲噳噴噵噷噸噹噺噽\",7,\"嚇\",6,\"嚐嚑嚒嚔\",14,\"嚤\",10,\"嚰\",6,\"嚸嚹嚺嚻嚽\",12,\"囋\",8,\"囕囖囘囙囜団囥\",5,\"囬囮囯囲図囶囷囸囻囼圀圁圂圅圇國\",6],[\"8840\",\"園\",9,\"圝圞圠圡圢圤圥圦圧圫圱圲圴\",4,\"圼圽圿坁坃坄坅坆坈坉坋坒\",4,\"坘坙坢坣坥坧坬坮坰坱坲坴坵坸坹坺坽坾坿垀\"],[\"8880\",\"垁垇垈垉垊垍\",4,\"垔\",6,\"垜垝垞垟垥垨垪垬垯垰垱垳垵垶垷垹\",8,\"埄\",6,\"埌埍埐埑埓埖埗埛埜埞埡埢埣埥\",7,\"埮埰埱埲埳埵埶執埻埼埾埿堁堃堄堅堈堉堊堌堎堏堐堒堓堔堖堗堘堚堛堜堝堟堢堣堥\",4,\"堫\",4,\"報堲堳場堶\",7],[\"8940\",\"堾\",5,\"塅\",6,\"塎塏塐塒塓塕塖塗塙\",4,\"塟\",5,\"塦\",4,\"塭\",16,\"塿墂墄墆墇墈墊墋墌\"],[\"8980\",\"墍\",4,\"墔\",4,\"墛墜墝墠\",7,\"墪\",17,\"墽墾墿壀壂壃壄壆\",10,\"壒壓壔壖\",13,\"壥\",5,\"壭壯壱売壴壵壷壸壺\",7,\"夃夅夆夈\",4,\"夎夐夑夒夓夗夘夛夝夞夠夡夢夣夦夨夬夰夲夳夵夶夻\"],[\"8a40\",\"夽夾夿奀奃奅奆奊奌奍奐奒奓奙奛\",4,\"奡奣奤奦\",12,\"奵奷奺奻奼奾奿妀妅妉妋妌妎妏妐妑妔妕妘妚妛妜妝妟妠妡妢妦\"],[\"8a80\",\"妧妬妭妰妱妳\",5,\"妺妼妽妿\",6,\"姇姈姉姌姍姎姏姕姖姙姛姞\",4,\"姤姦姧姩姪姫姭\",11,\"姺姼姽姾娀娂娊娋娍娎娏娐娒娔娕娖娗娙娚娛娝娞娡娢娤娦娧娨娪\",6,\"娳娵娷\",4,\"娽娾娿婁\",4,\"婇婈婋\",9,\"婖婗婘婙婛\",5],[\"8b40\",\"婡婣婤婥婦婨婩婫\",8,\"婸婹婻婼婽婾媀\",17,\"媓\",6,\"媜\",13,\"媫媬\"],[\"8b80\",\"媭\",4,\"媴媶媷媹\",4,\"媿嫀嫃\",5,\"嫊嫋嫍\",4,\"嫓嫕嫗嫙嫚嫛嫝嫞嫟嫢嫤嫥嫧嫨嫪嫬\",4,\"嫲\",22,\"嬊\",11,\"嬘\",25,\"嬳嬵嬶嬸\",7,\"孁\",6],[\"8c40\",\"孈\",7,\"孒孖孞孠孡孧孨孫孭孮孯孲孴孶孷學孹孻孼孾孿宂宆宊宍宎宐宑宒宔宖実宧宨宩宬宭宮宯宱宲宷宺宻宼寀寁寃寈寉寊寋寍寎寏\"],[\"8c80\",\"寑寔\",8,\"寠寢寣實寧審\",4,\"寯寱\",6,\"寽対尀専尃尅將專尋尌對導尐尒尓尗尙尛尞尟尠尡尣尦尨尩尪尫尭尮尯尰尲尳尵尶尷屃屄屆屇屌屍屒屓屔屖屗屘屚屛屜屝屟屢層屧\",6,\"屰屲\",6,\"屻屼屽屾岀岃\",4,\"岉岊岋岎岏岒岓岕岝\",4,\"岤\",4],[\"8d40\",\"岪岮岯岰岲岴岶岹岺岻岼岾峀峂峃峅\",5,\"峌\",5,\"峓\",5,\"峚\",6,\"峢峣峧峩峫峬峮峯峱\",9,\"峼\",4],[\"8d80\",\"崁崄崅崈\",5,\"崏\",4,\"崕崗崘崙崚崜崝崟\",4,\"崥崨崪崫崬崯\",4,\"崵\",7,\"崿\",7,\"嵈嵉嵍\",10,\"嵙嵚嵜嵞\",10,\"嵪嵭嵮嵰嵱嵲嵳嵵\",12,\"嶃\",21,\"嶚嶛嶜嶞嶟嶠\"],[\"8e40\",\"嶡\",21,\"嶸\",12,\"巆\",6,\"巎\",12,\"巜巟巠巣巤巪巬巭\"],[\"8e80\",\"巰巵巶巸\",4,\"巿帀帄帇帉帊帋帍帎帒帓帗帞\",7,\"帨\",4,\"帯帰帲\",4,\"帹帺帾帿幀幁幃幆\",5,\"幍\",6,\"幖\",4,\"幜幝幟幠幣\",14,\"幵幷幹幾庁庂広庅庈庉庌庍庎庒庘庛庝庡庢庣庤庨\",4,\"庮\",4,\"庴庺庻庼庽庿\",6],[\"8f40\",\"廆廇廈廋\",5,\"廔廕廗廘廙廚廜\",11,\"廩廫\",8,\"廵廸廹廻廼廽弅弆弇弉弌弍弎弐弒弔弖弙弚弜弝弞弡弢弣弤\"],[\"8f80\",\"弨弫弬弮弰弲\",6,\"弻弽弾弿彁\",14,\"彑彔彙彚彛彜彞彟彠彣彥彧彨彫彮彯彲彴彵彶彸彺彽彾彿徃徆徍徎徏徑従徔徖徚徛徝從徟徠徢\",5,\"復徫徬徯\",5,\"徶徸徹徺徻徾\",4,\"忇忈忊忋忎忓忔忕忚忛応忞忟忢忣忥忦忨忩忬忯忰忲忳忴忶忷忹忺忼怇\"],[\"9040\",\"怈怉怋怌怐怑怓怗怘怚怞怟怢怣怤怬怭怮怰\",4,\"怶\",4,\"怽怾恀恄\",6,\"恌恎恏恑恓恔恖恗恘恛恜恞恟恠恡恥恦恮恱恲恴恵恷恾悀\"],[\"9080\",\"悁悂悅悆悇悈悊悋悎悏悐悑悓悕悗悘悙悜悞悡悢悤悥悧悩悪悮悰悳悵悶悷悹悺悽\",7,\"惇惈惉惌\",4,\"惒惓惔惖惗惙惛惞惡\",4,\"惪惱惲惵惷惸惻\",4,\"愂愃愄愅愇愊愋愌愐\",4,\"愖愗愘愙愛愜愝愞愡愢愥愨愩愪愬\",18,\"慀\",6],[\"9140\",\"慇慉態慍慏慐慒慓慔慖\",6,\"慞慟慠慡慣慤慥慦慩\",6,\"慱慲慳慴慶慸\",18,\"憌憍憏\",4,\"憕\"],[\"9180\",\"憖\",6,\"憞\",8,\"憪憫憭\",9,\"憸\",5,\"憿懀懁懃\",4,\"應懌\",4,\"懓懕\",16,\"懧\",13,\"懶\",8,\"戀\",5,\"戇戉戓戔戙戜戝戞戠戣戦戧戨戩戫戭戯戰戱戲戵戶戸\",4,\"扂扄扅扆扊\"],[\"9240\",\"扏扐払扖扗扙扚扜\",6,\"扤扥扨扱扲扴扵扷扸扺扻扽抁抂抃抅抆抇抈抋\",5,\"抔抙抜抝択抣抦抧抩抪抭抮抯抰抲抳抴抶抷抸抺抾拀拁\"],[\"9280\",\"拃拋拏拑拕拝拞拠拡拤拪拫拰拲拵拸拹拺拻挀挃挄挅挆挊挋挌挍挏挐挒挓挔挕挗挘挙挜挦挧挩挬挭挮挰挱挳\",5,\"挻挼挾挿捀捁捄捇捈捊捑捒捓捔捖\",7,\"捠捤捥捦捨捪捫捬捯捰捲捳捴捵捸捹捼捽捾捿掁掃掄掅掆掋掍掑掓掔掕掗掙\",6,\"採掤掦掫掯掱掲掵掶掹掻掽掿揀\"],[\"9340\",\"揁揂揃揅揇揈揊揋揌揑揓揔揕揗\",6,\"揟揢揤\",4,\"揫揬揮揯揰揱揳揵揷揹揺揻揼揾搃搄搆\",4,\"損搎搑搒搕\",5,\"搝搟搢搣搤\"],[\"9380\",\"搥搧搨搩搫搮\",5,\"搵\",4,\"搻搼搾摀摂摃摉摋\",6,\"摓摕摖摗摙\",4,\"摟\",7,\"摨摪摫摬摮\",9,\"摻\",6,\"撃撆撈\",8,\"撓撔撗撘撚撛撜撝撟\",4,\"撥撦撧撨撪撫撯撱撲撳撴撶撹撻撽撾撿擁擃擄擆\",6,\"擏擑擓擔擕擖擙據\"],[\"9440\",\"擛擜擝擟擠擡擣擥擧\",24,\"攁\",7,\"攊\",7,\"攓\",4,\"攙\",8],[\"9480\",\"攢攣攤攦\",4,\"攬攭攰攱攲攳攷攺攼攽敀\",4,\"敆敇敊敋敍敎敐敒敓敔敗敘敚敜敟敠敡敤敥敧敨敩敪敭敮敯敱敳敵敶數\",14,\"斈斉斊斍斎斏斒斔斕斖斘斚斝斞斠斢斣斦斨斪斬斮斱\",7,\"斺斻斾斿旀旂旇旈旉旊旍旐旑旓旔旕旘\",7,\"旡旣旤旪旫\"],[\"9540\",\"旲旳旴旵旸旹旻\",4,\"昁昄昅昇昈昉昋昍昐昑昒昖昗昘昚昛昜昞昡昢昣昤昦昩昪昫昬昮昰昲昳昷\",4,\"昽昿晀時晄\",6,\"晍晎晐晑晘\"],[\"9580\",\"晙晛晜晝晞晠晢晣晥晧晩\",4,\"晱晲晳晵晸晹晻晼晽晿暀暁暃暅暆暈暉暊暋暍暎暏暐暒暓暔暕暘\",4,\"暞\",8,\"暩\",4,\"暯\",4,\"暵暶暷暸暺暻暼暽暿\",25,\"曚曞\",7,\"曧曨曪\",5,\"曱曵曶書曺曻曽朁朂會\"],[\"9640\",\"朄朅朆朇朌朎朏朑朒朓朖朘朙朚朜朞朠\",5,\"朧朩朮朰朲朳朶朷朸朹朻朼朾朿杁杄杅杇杊杋杍杒杔杕杗\",4,\"杝杢杣杤杦杧杫杬杮東杴杶\"],[\"9680\",\"杸杹杺杻杽枀枂枃枅枆枈枊枌枍枎枏枑枒枓枔枖枙枛枟枠枡枤枦枩枬枮枱枲枴枹\",7,\"柂柅\",9,\"柕柖柗柛柟柡柣柤柦柧柨柪柫柭柮柲柵\",7,\"柾栁栂栃栄栆栍栐栒栔栕栘\",4,\"栞栟栠栢\",6,\"栫\",6,\"栴栵栶栺栻栿桇桋桍桏桒桖\",5],[\"9740\",\"桜桝桞桟桪桬\",7,\"桵桸\",8,\"梂梄梇\",7,\"梐梑梒梔梕梖梘\",9,\"梣梤梥梩梪梫梬梮梱梲梴梶梷梸\"],[\"9780\",\"梹\",6,\"棁棃\",5,\"棊棌棎棏棐棑棓棔棖棗棙棛\",4,\"棡棢棤\",9,\"棯棲棳棴棶棷棸棻棽棾棿椀椂椃椄椆\",4,\"椌椏椑椓\",11,\"椡椢椣椥\",7,\"椮椯椱椲椳椵椶椷椸椺椻椼椾楀楁楃\",16,\"楕楖楘楙楛楜楟\"],[\"9840\",\"楡楢楤楥楧楨楩楪楬業楯楰楲\",4,\"楺楻楽楾楿榁榃榅榊榋榌榎\",5,\"榖榗榙榚榝\",9,\"榩榪榬榮榯榰榲榳榵榶榸榹榺榼榽\"],[\"9880\",\"榾榿槀槂\",7,\"構槍槏槑槒槓槕\",5,\"槜槝槞槡\",11,\"槮槯槰槱槳\",9,\"槾樀\",9,\"樋\",11,\"標\",5,\"樠樢\",5,\"権樫樬樭樮樰樲樳樴樶\",6,\"樿\",4,\"橅橆橈\",7,\"橑\",6,\"橚\"],[\"9940\",\"橜\",4,\"橢橣橤橦\",10,\"橲\",6,\"橺橻橽橾橿檁檂檃檅\",8,\"檏檒\",4,\"檘\",7,\"檡\",5],[\"9980\",\"檧檨檪檭\",114,\"欥欦欨\",6],[\"9a40\",\"欯欰欱欳欴欵欶欸欻欼欽欿歀歁歂歄歅歈歊歋歍\",11,\"歚\",7,\"歨歩歫\",13,\"歺歽歾歿殀殅殈\"],[\"9a80\",\"殌殎殏殐殑殔殕殗殘殙殜\",4,\"殢\",7,\"殫\",7,\"殶殸\",6,\"毀毃毄毆\",4,\"毌毎毐毑毘毚毜\",4,\"毢\",7,\"毬毭毮毰毱毲毴毶毷毸毺毻毼毾\",6,\"氈\",4,\"氎氒気氜氝氞氠氣氥氫氬氭氱氳氶氷氹氺氻氼氾氿汃汄汅汈汋\",4,\"汑汒汓汖汘\"],[\"9b40\",\"汙汚汢汣汥汦汧汫\",4,\"汱汳汵汷汸決汻汼汿沀沄沇沊沋沍沎沑沒沕沖沗沘沚沜沝沞沠沢沨沬沯沰沴沵沶沷沺泀況泂泃泆泇泈泋泍泎泏泑泒泘\"],[\"9b80\",\"泙泚泜泝泟泤泦泧泩泬泭泲泴泹泿洀洂洃洅洆洈洉洊洍洏洐洑洓洔洕洖洘洜洝洟\",5,\"洦洨洩洬洭洯洰洴洶洷洸洺洿浀浂浄浉浌浐浕浖浗浘浛浝浟浡浢浤浥浧浨浫浬浭浰浱浲浳浵浶浹浺浻浽\",4,\"涃涄涆涇涊涋涍涏涐涒涖\",4,\"涜涢涥涬涭涰涱涳涴涶涷涹\",5,\"淁淂淃淈淉淊\"],[\"9c40\",\"淍淎淏淐淒淓淔淕淗淚淛淜淟淢淣淥淧淨淩淪淭淯淰淲淴淵淶淸淺淽\",7,\"渆渇済渉渋渏渒渓渕渘渙減渜渞渟渢渦渧渨渪測渮渰渱渳渵\"],[\"9c80\",\"渶渷渹渻\",7,\"湅\",7,\"湏湐湑湒湕湗湙湚湜湝湞湠\",10,\"湬湭湯\",14,\"満溁溂溄溇溈溊\",4,\"溑\",6,\"溙溚溛溝溞溠溡溣溤溦溨溩溫溬溭溮溰溳溵溸溹溼溾溿滀滃滄滅滆滈滉滊滌滍滎滐滒滖滘滙滛滜滝滣滧滪\",5],[\"9d40\",\"滰滱滲滳滵滶滷滸滺\",7,\"漃漄漅漇漈漊\",4,\"漐漑漒漖\",9,\"漡漢漣漥漦漧漨漬漮漰漲漴漵漷\",6,\"漿潀潁潂\"],[\"9d80\",\"潃潄潅潈潉潊潌潎\",9,\"潙潚潛潝潟潠潡潣潤潥潧\",5,\"潯潰潱潳潵潶潷潹潻潽\",6,\"澅澆澇澊澋澏\",12,\"澝澞澟澠澢\",4,\"澨\",10,\"澴澵澷澸澺\",5,\"濁濃\",5,\"濊\",6,\"濓\",10,\"濟濢濣濤濥\"],[\"9e40\",\"濦\",7,\"濰\",32,\"瀒\",7,\"瀜\",6,\"瀤\",6],[\"9e80\",\"瀫\",9,\"瀶瀷瀸瀺\",17,\"灍灎灐\",13,\"灟\",11,\"灮灱灲灳灴灷灹灺灻災炁炂炃炄炆炇炈炋炌炍炏炐炑炓炗炘炚炛炞\",12,\"炰炲炴炵炶為炾炿烄烅烆烇烉烋\",12,\"烚\"],[\"9f40\",\"烜烝烞烠烡烢烣烥烪烮烰\",6,\"烸烺烻烼烾\",10,\"焋\",4,\"焑焒焔焗焛\",10,\"焧\",7,\"焲焳焴\"],[\"9f80\",\"焵焷\",13,\"煆煇煈煉煋煍煏\",12,\"煝煟\",4,\"煥煩\",4,\"煯煰煱煴煵煶煷煹煻煼煾\",5,\"熅\",4,\"熋熌熍熎熐熑熒熓熕熖熗熚\",4,\"熡\",6,\"熩熪熫熭\",5,\"熴熶熷熸熺\",8,\"燄\",9,\"燏\",4],[\"a040\",\"燖\",9,\"燡燢燣燤燦燨\",5,\"燯\",9,\"燺\",11,\"爇\",19],[\"a080\",\"爛爜爞\",9,\"爩爫爭爮爯爲爳爴爺爼爾牀\",6,\"牉牊牋牎牏牐牑牓牔牕牗牘牚牜牞牠牣牤牥牨牪牫牬牭牰牱牳牴牶牷牸牻牼牽犂犃犅\",4,\"犌犎犐犑犓\",11,\"犠\",11,\"犮犱犲犳犵犺\",6,\"狅狆狇狉狊狋狌狏狑狓狔狕狖狘狚狛\"],[\"a1a1\",\"　、。·ˉˇ¨〃々—～‖…‘’“”〔〕〈\",7,\"〖〗【】±×÷∶∧∨∑∏∪∩∈∷√⊥∥∠⌒⊙∫∮≡≌≈∽∝≠≮≯≤≥∞∵∴♂♀°′″℃＄¤￠￡‰§№☆★○●◎◇◆□■△▲※→←↑↓〓\"],[\"a2a1\",\"ⅰ\",9],[\"a2b1\",\"⒈\",19,\"⑴\",19,\"①\",9],[\"a2e5\",\"㈠\",9],[\"a2f1\",\"Ⅰ\",11],[\"a3a1\",\"！＂＃￥％\",88,\"￣\"],[\"a4a1\",\"ぁ\",82],[\"a5a1\",\"ァ\",85],[\"a6a1\",\"Α\",16,\"Σ\",6],[\"a6c1\",\"α\",16,\"σ\",6],[\"a6e0\",\"︵︶︹︺︿﹀︽︾﹁﹂﹃﹄\"],[\"a6ee\",\"︻︼︷︸︱\"],[\"a6f4\",\"︳︴\"],[\"a7a1\",\"А\",5,\"ЁЖ\",25],[\"a7d1\",\"а\",5,\"ёж\",25],[\"a840\",\"ˊˋ˙–―‥‵℅℉↖↗↘↙∕∟∣≒≦≧⊿═\",35,\"▁\",6],[\"a880\",\"█\",7,\"▓▔▕▼▽◢◣◤◥☉⊕〒〝〞\"],[\"a8a1\",\"āáǎàēéěèīíǐìōóǒòūúǔùǖǘǚǜüêɑ\"],[\"a8bd\",\"ńň\"],[\"a8c0\",\"ɡ\"],[\"a8c5\",\"ㄅ\",36],[\"a940\",\"〡\",8,\"㊣㎎㎏㎜㎝㎞㎡㏄㏎㏑㏒㏕︰￢￤\"],[\"a959\",\"℡㈱\"],[\"a95c\",\"‐\"],[\"a960\",\"ー゛゜ヽヾ〆ゝゞ﹉\",9,\"﹔﹕﹖﹗﹙\",8],[\"a980\",\"﹢\",4,\"﹨﹩﹪﹫\"],[\"a996\",\"〇\"],[\"a9a4\",\"─\",75],[\"aa40\",\"狜狝狟狢\",5,\"狪狫狵狶狹狽狾狿猀猂猄\",5,\"猋猌猍猏猐猑猒猔猘猙猚猟猠猣猤猦猧猨猭猯猰猲猳猵猶猺猻猼猽獀\",8],[\"aa80\",\"獉獊獋獌獎獏獑獓獔獕獖獘\",7,\"獡\",10,\"獮獰獱\"],[\"ab40\",\"獲\",11,\"獿\",4,\"玅玆玈玊玌玍玏玐玒玓玔玕玗玘玙玚玜玝玞玠玡玣\",5,\"玪玬玭玱玴玵玶玸玹玼玽玾玿珁珃\",4],[\"ab80\",\"珋珌珎珒\",6,\"珚珛珜珝珟珡珢珣珤珦珨珪珫珬珮珯珰珱珳\",4],[\"ac40\",\"珸\",10,\"琄琇琈琋琌琍琎琑\",8,\"琜\",5,\"琣琤琧琩琫琭琯琱琲琷\",4,\"琽琾琿瑀瑂\",11],[\"ac80\",\"瑎\",6,\"瑖瑘瑝瑠\",12,\"瑮瑯瑱\",4,\"瑸瑹瑺\"],[\"ad40\",\"瑻瑼瑽瑿璂璄璅璆璈璉璊璌璍璏璑\",10,\"璝璟\",7,\"璪\",15,\"璻\",12],[\"ad80\",\"瓈\",9,\"瓓\",8,\"瓝瓟瓡瓥瓧\",6,\"瓰瓱瓲\"],[\"ae40\",\"瓳瓵瓸\",6,\"甀甁甂甃甅\",7,\"甎甐甒甔甕甖甗甛甝甞甠\",4,\"甦甧甪甮甴甶甹甼甽甿畁畂畃畄畆畇畉畊畍畐畑畒畓畕畖畗畘\"],[\"ae80\",\"畝\",7,\"畧畨畩畫\",6,\"畳畵當畷畺\",4,\"疀疁疂疄疅疇\"],[\"af40\",\"疈疉疊疌疍疎疐疓疕疘疛疜疞疢疦\",4,\"疭疶疷疺疻疿痀痁痆痋痌痎痏痐痑痓痗痙痚痜痝痟痠痡痥痩痬痭痮痯痲痳痵痶痷痸痺痻痽痾瘂瘄瘆瘇\"],[\"af80\",\"瘈瘉瘋瘍瘎瘏瘑瘒瘓瘔瘖瘚瘜瘝瘞瘡瘣瘧瘨瘬瘮瘯瘱瘲瘶瘷瘹瘺瘻瘽癁療癄\"],[\"b040\",\"癅\",6,\"癎\",5,\"癕癗\",4,\"癝癟癠癡癢癤\",6,\"癬癭癮癰\",7,\"癹発發癿皀皁皃皅皉皊皌皍皏皐皒皔皕皗皘皚皛\"],[\"b080\",\"皜\",7,\"皥\",8,\"皯皰皳皵\",9,\"盀盁盃啊阿埃挨哎唉哀皑癌蔼矮艾碍爱隘鞍氨安俺按暗岸胺案肮昂盎凹敖熬翱袄傲奥懊澳芭捌扒叭吧笆八疤巴拔跋靶把耙坝霸罢爸白柏百摆佰败拜稗斑班搬扳般颁板版扮拌伴瓣半办绊邦帮梆榜膀绑棒磅蚌镑傍谤苞胞包褒剥\"],[\"b140\",\"盄盇盉盋盌盓盕盙盚盜盝盞盠\",4,\"盦\",7,\"盰盳盵盶盷盺盻盽盿眀眂眃眅眆眊県眎\",10,\"眛眜眝眞眡眣眤眥眧眪眫\"],[\"b180\",\"眬眮眰\",4,\"眹眻眽眾眿睂睄睅睆睈\",7,\"睒\",7,\"睜薄雹保堡饱宝抱报暴豹鲍爆杯碑悲卑北辈背贝钡倍狈备惫焙被奔苯本笨崩绷甭泵蹦迸逼鼻比鄙笔彼碧蓖蔽毕毙毖币庇痹闭敝弊必辟壁臂避陛鞭边编贬扁便变卞辨辩辫遍标彪膘表鳖憋别瘪彬斌濒滨宾摈兵冰柄丙秉饼炳\"],[\"b240\",\"睝睞睟睠睤睧睩睪睭\",11,\"睺睻睼瞁瞂瞃瞆\",5,\"瞏瞐瞓\",11,\"瞡瞣瞤瞦瞨瞫瞭瞮瞯瞱瞲瞴瞶\",4],[\"b280\",\"瞼瞾矀\",12,\"矎\",8,\"矘矙矚矝\",4,\"矤病并玻菠播拨钵波博勃搏铂箔伯帛舶脖膊渤泊驳捕卜哺补埠不布步簿部怖擦猜裁材才财睬踩采彩菜蔡餐参蚕残惭惨灿苍舱仓沧藏操糙槽曹草厕策侧册测层蹭插叉茬茶查碴搽察岔差诧拆柴豺搀掺蝉馋谗缠铲产阐颤昌猖\"],[\"b340\",\"矦矨矪矯矰矱矲矴矵矷矹矺矻矼砃\",5,\"砊砋砎砏砐砓砕砙砛砞砠砡砢砤砨砪砫砮砯砱砲砳砵砶砽砿硁硂硃硄硆硈硉硊硋硍硏硑硓硔硘硙硚\"],[\"b380\",\"硛硜硞\",11,\"硯\",7,\"硸硹硺硻硽\",6,\"场尝常长偿肠厂敞畅唱倡超抄钞朝嘲潮巢吵炒车扯撤掣彻澈郴臣辰尘晨忱沉陈趁衬撑称城橙成呈乘程惩澄诚承逞骋秤吃痴持匙池迟弛驰耻齿侈尺赤翅斥炽充冲虫崇宠抽酬畴踌稠愁筹仇绸瞅丑臭初出橱厨躇锄雏滁除楚\"],[\"b440\",\"碄碅碆碈碊碋碏碐碒碔碕碖碙碝碞碠碢碤碦碨\",7,\"碵碶碷碸確碻碼碽碿磀磂磃磄磆磇磈磌磍磎磏磑磒磓磖磗磘磚\",9],[\"b480\",\"磤磥磦磧磩磪磫磭\",4,\"磳磵磶磸磹磻\",5,\"礂礃礄礆\",6,\"础储矗搐触处揣川穿椽传船喘串疮窗幢床闯创吹炊捶锤垂春椿醇唇淳纯蠢戳绰疵茨磁雌辞慈瓷词此刺赐次聪葱囱匆从丛凑粗醋簇促蹿篡窜摧崔催脆瘁粹淬翠村存寸磋撮搓措挫错搭达答瘩打大呆歹傣戴带殆代贷袋待逮\"],[\"b540\",\"礍\",5,\"礔\",9,\"礟\",4,\"礥\",14,\"礵\",4,\"礽礿祂祃祄祅祇祊\",8,\"祔祕祘祙祡祣\"],[\"b580\",\"祤祦祩祪祫祬祮祰\",6,\"祹祻\",4,\"禂禃禆禇禈禉禋禌禍禎禐禑禒怠耽担丹单郸掸胆旦氮但惮淡诞弹蛋当挡党荡档刀捣蹈倒岛祷导到稻悼道盗德得的蹬灯登等瞪凳邓堤低滴迪敌笛狄涤翟嫡抵底地蒂第帝弟递缔颠掂滇碘点典靛垫电佃甸店惦奠淀殿碉叼雕凋刁掉吊钓调跌爹碟蝶迭谍叠\"],[\"b640\",\"禓\",6,\"禛\",11,\"禨\",10,\"禴\",4,\"禼禿秂秄秅秇秈秊秌秎秏秐秓秔秖秗秙\",5,\"秠秡秢秥秨秪\"],[\"b680\",\"秬秮秱\",6,\"秹秺秼秾秿稁稄稅稇稈稉稊稌稏\",4,\"稕稖稘稙稛稜丁盯叮钉顶鼎锭定订丢东冬董懂动栋侗恫冻洞兜抖斗陡豆逗痘都督毒犊独读堵睹赌杜镀肚度渡妒端短锻段断缎堆兑队对墩吨蹲敦顿囤钝盾遁掇哆多夺垛躲朵跺舵剁惰堕蛾峨鹅俄额讹娥恶厄扼遏鄂饿恩而儿耳尔饵洱二\"],[\"b740\",\"稝稟稡稢稤\",14,\"稴稵稶稸稺稾穀\",5,\"穇\",9,\"穒\",4,\"穘\",16],[\"b780\",\"穩\",6,\"穱穲穳穵穻穼穽穾窂窅窇窉窊窋窌窎窏窐窓窔窙窚窛窞窡窢贰发罚筏伐乏阀法珐藩帆番翻樊矾钒繁凡烦反返范贩犯饭泛坊芳方肪房防妨仿访纺放菲非啡飞肥匪诽吠肺废沸费芬酚吩氛分纷坟焚汾粉奋份忿愤粪丰封枫蜂峰锋风疯烽逢冯缝讽奉凤佛否夫敷肤孵扶拂辐幅氟符伏俘服\"],[\"b840\",\"窣窤窧窩窪窫窮\",4,\"窴\",10,\"竀\",10,\"竌\",9,\"竗竘竚竛竜竝竡竢竤竧\",5,\"竮竰竱竲竳\"],[\"b880\",\"竴\",4,\"竻竼竾笀笁笂笅笇笉笌笍笎笐笒笓笖笗笘笚笜笝笟笡笢笣笧笩笭浮涪福袱弗甫抚辅俯釜斧脯腑府腐赴副覆赋复傅付阜父腹负富讣附妇缚咐噶嘎该改概钙盖溉干甘杆柑竿肝赶感秆敢赣冈刚钢缸肛纲岗港杠篙皋高膏羔糕搞镐稿告哥歌搁戈鸽胳疙割革葛格蛤阁隔铬个各给根跟耕更庚羹\"],[\"b940\",\"笯笰笲笴笵笶笷笹笻笽笿\",5,\"筆筈筊筍筎筓筕筗筙筜筞筟筡筣\",10,\"筯筰筳筴筶筸筺筼筽筿箁箂箃箄箆\",6,\"箎箏\"],[\"b980\",\"箑箒箓箖箘箙箚箛箞箟箠箣箤箥箮箯箰箲箳箵箶箷箹\",7,\"篂篃範埂耿梗工攻功恭龚供躬公宫弓巩汞拱贡共钩勾沟苟狗垢构购够辜菇咕箍估沽孤姑鼓古蛊骨谷股故顾固雇刮瓜剐寡挂褂乖拐怪棺关官冠观管馆罐惯灌贯光广逛瑰规圭硅归龟闺轨鬼诡癸桂柜跪贵刽辊滚棍锅郭国果裹过哈\"],[\"ba40\",\"篅篈築篊篋篍篎篏篐篒篔\",4,\"篛篜篞篟篠篢篣篤篧篨篩篫篬篭篯篰篲\",4,\"篸篹篺篻篽篿\",7,\"簈簉簊簍簎簐\",5,\"簗簘簙\"],[\"ba80\",\"簚\",4,\"簠\",5,\"簨簩簫\",12,\"簹\",5,\"籂骸孩海氦亥害骇酣憨邯韩含涵寒函喊罕翰撼捍旱憾悍焊汗汉夯杭航壕嚎豪毫郝好耗号浩呵喝荷菏核禾和何合盒貉阂河涸赫褐鹤贺嘿黑痕很狠恨哼亨横衡恒轰哄烘虹鸿洪宏弘红喉侯猴吼厚候后呼乎忽瑚壶葫胡蝴狐糊湖\"],[\"bb40\",\"籃\",9,\"籎\",36,\"籵\",5,\"籾\",9],[\"bb80\",\"粈粊\",6,\"粓粔粖粙粚粛粠粡粣粦粧粨粩粫粬粭粯粰粴\",4,\"粺粻弧虎唬护互沪户花哗华猾滑画划化话槐徊怀淮坏欢环桓还缓换患唤痪豢焕涣宦幻荒慌黄磺蝗簧皇凰惶煌晃幌恍谎灰挥辉徽恢蛔回毁悔慧卉惠晦贿秽会烩汇讳诲绘荤昏婚魂浑混豁活伙火获或惑霍货祸击圾基机畸稽积箕\"],[\"bc40\",\"粿糀糂糃糄糆糉糋糎\",6,\"糘糚糛糝糞糡\",6,\"糩\",5,\"糰\",7,\"糹糺糼\",13,\"紋\",5],[\"bc80\",\"紑\",14,\"紡紣紤紥紦紨紩紪紬紭紮細\",6,\"肌饥迹激讥鸡姬绩缉吉极棘辑籍集及急疾汲即嫉级挤几脊己蓟技冀季伎祭剂悸济寄寂计记既忌际妓继纪嘉枷夹佳家加荚颊贾甲钾假稼价架驾嫁歼监坚尖笺间煎兼肩艰奸缄茧检柬碱硷拣捡简俭剪减荐槛鉴践贱见键箭件\"],[\"bd40\",\"紷\",54,\"絯\",7],[\"bd80\",\"絸\",32,\"健舰剑饯渐溅涧建僵姜将浆江疆蒋桨奖讲匠酱降蕉椒礁焦胶交郊浇骄娇嚼搅铰矫侥脚狡角饺缴绞剿教酵轿较叫窖揭接皆秸街阶截劫节桔杰捷睫竭洁结解姐戒藉芥界借介疥诫届巾筋斤金今津襟紧锦仅谨进靳晋禁近烬浸\"],[\"be40\",\"継\",12,\"綧\",6,\"綯\",42],[\"be80\",\"線\",32,\"尽劲荆兢茎睛晶鲸京惊精粳经井警景颈静境敬镜径痉靖竟竞净炯窘揪究纠玖韭久灸九酒厩救旧臼舅咎就疚鞠拘狙疽居驹菊局咀矩举沮聚拒据巨具距踞锯俱句惧炬剧捐鹃娟倦眷卷绢撅攫抉掘倔爵觉决诀绝均菌钧军君峻\"],[\"bf40\",\"緻\",62],[\"bf80\",\"縺縼\",4,\"繂\",4,\"繈\",21,\"俊竣浚郡骏喀咖卡咯开揩楷凯慨刊堪勘坎砍看康慷糠扛抗亢炕考拷烤靠坷苛柯棵磕颗科壳咳可渴克刻客课肯啃垦恳坑吭空恐孔控抠口扣寇枯哭窟苦酷库裤夸垮挎跨胯块筷侩快宽款匡筐狂框矿眶旷况亏盔岿窥葵奎魁傀\"],[\"c040\",\"繞\",35,\"纃\",23,\"纜纝纞\"],[\"c080\",\"纮纴纻纼绖绤绬绹缊缐缞缷缹缻\",6,\"罃罆\",9,\"罒罓馈愧溃坤昆捆困括扩廓阔垃拉喇蜡腊辣啦莱来赖蓝婪栏拦篮阑兰澜谰揽览懒缆烂滥琅榔狼廊郎朗浪捞劳牢老佬姥酪烙涝勒乐雷镭蕾磊累儡垒擂肋类泪棱楞冷厘梨犁黎篱狸离漓理李里鲤礼莉荔吏栗丽厉励砾历利傈例俐\"],[\"c140\",\"罖罙罛罜罝罞罠罣\",4,\"罫罬罭罯罰罳罵罶罷罸罺罻罼罽罿羀羂\",7,\"羋羍羏\",4,\"羕\",4,\"羛羜羠羢羣羥羦羨\",6,\"羱\"],[\"c180\",\"羳\",4,\"羺羻羾翀翂翃翄翆翇翈翉翋翍翏\",4,\"翖翗翙\",5,\"翢翣痢立粒沥隶力璃哩俩联莲连镰廉怜涟帘敛脸链恋炼练粮凉梁粱良两辆量晾亮谅撩聊僚疗燎寥辽潦了撂镣廖料列裂烈劣猎琳林磷霖临邻鳞淋凛赁吝拎玲菱零龄铃伶羚凌灵陵岭领另令溜琉榴硫馏留刘瘤流柳六龙聋咙笼窿\"],[\"c240\",\"翤翧翨翪翫翬翭翯翲翴\",6,\"翽翾翿耂耇耈耉耊耎耏耑耓耚耛耝耞耟耡耣耤耫\",5,\"耲耴耹耺耼耾聀聁聄聅聇聈聉聎聏聐聑聓聕聖聗\"],[\"c280\",\"聙聛\",13,\"聫\",5,\"聲\",11,\"隆垄拢陇楼娄搂篓漏陋芦卢颅庐炉掳卤虏鲁麓碌露路赂鹿潞禄录陆戮驴吕铝侣旅履屡缕虑氯律率滤绿峦挛孪滦卵乱掠略抡轮伦仑沦纶论萝螺罗逻锣箩骡裸落洛骆络妈麻玛码蚂马骂嘛吗埋买麦卖迈脉瞒馒蛮满蔓曼慢漫\"],[\"c340\",\"聾肁肂肅肈肊肍\",5,\"肔肕肗肙肞肣肦肧肨肬肰肳肵肶肸肹肻胅胇\",4,\"胏\",6,\"胘胟胠胢胣胦胮胵胷胹胻胾胿脀脁脃脄脅脇脈脋\"],[\"c380\",\"脌脕脗脙脛脜脝脟\",12,\"脭脮脰脳脴脵脷脹\",4,\"脿谩芒茫盲氓忙莽猫茅锚毛矛铆卯茂冒帽貌贸么玫枚梅酶霉煤没眉媒镁每美昧寐妹媚门闷们萌蒙檬盟锰猛梦孟眯醚靡糜迷谜弥米秘觅泌蜜密幂棉眠绵冕免勉娩缅面苗描瞄藐秒渺庙妙蔑灭民抿皿敏悯闽明螟鸣铭名命谬摸\"],[\"c440\",\"腀\",5,\"腇腉腍腎腏腒腖腗腘腛\",4,\"腡腢腣腤腦腨腪腫腬腯腲腳腵腶腷腸膁膃\",4,\"膉膋膌膍膎膐膒\",5,\"膙膚膞\",4,\"膤膥\"],[\"c480\",\"膧膩膫\",7,\"膴\",5,\"膼膽膾膿臄臅臇臈臉臋臍\",6,\"摹蘑模膜磨摩魔抹末莫墨默沫漠寞陌谋牟某拇牡亩姆母墓暮幕募慕木目睦牧穆拿哪呐钠那娜纳氖乃奶耐奈南男难囊挠脑恼闹淖呢馁内嫩能妮霓倪泥尼拟你匿腻逆溺蔫拈年碾撵捻念娘酿鸟尿捏聂孽啮镊镍涅您柠狞凝宁\"],[\"c540\",\"臔\",14,\"臤臥臦臨臩臫臮\",4,\"臵\",5,\"臽臿舃與\",4,\"舎舏舑舓舕\",5,\"舝舠舤舥舦舧舩舮舲舺舼舽舿\"],[\"c580\",\"艀艁艂艃艅艆艈艊艌艍艎艐\",7,\"艙艛艜艝艞艠\",7,\"艩拧泞牛扭钮纽脓浓农弄奴努怒女暖虐疟挪懦糯诺哦欧鸥殴藕呕偶沤啪趴爬帕怕琶拍排牌徘湃派攀潘盘磐盼畔判叛乓庞旁耪胖抛咆刨炮袍跑泡呸胚培裴赔陪配佩沛喷盆砰抨烹澎彭蓬棚硼篷膨朋鹏捧碰坯砒霹批披劈琵毗\"],[\"c640\",\"艪艫艬艭艱艵艶艷艸艻艼芀芁芃芅芆芇芉芌芐芓芔芕芖芚芛芞芠芢芣芧芲芵芶芺芻芼芿苀苂苃苅苆苉苐苖苙苚苝苢苧苨苩苪苬苭苮苰苲苳苵苶苸\"],[\"c680\",\"苺苼\",4,\"茊茋茍茐茒茓茖茘茙茝\",9,\"茩茪茮茰茲茷茻茽啤脾疲皮匹痞僻屁譬篇偏片骗飘漂瓢票撇瞥拼频贫品聘乒坪苹萍平凭瓶评屏坡泼颇婆破魄迫粕剖扑铺仆莆葡菩蒲埔朴圃普浦谱曝瀑期欺栖戚妻七凄漆柒沏其棋奇歧畦崎脐齐旗祈祁骑起岂乞企启契砌器气迄弃汽泣讫掐\"],[\"c740\",\"茾茿荁荂荄荅荈荊\",4,\"荓荕\",4,\"荝荢荰\",6,\"荹荺荾\",6,\"莇莈莊莋莌莍莏莐莑莔莕莖莗莙莚莝莟莡\",6,\"莬莭莮\"],[\"c780\",\"莯莵莻莾莿菂菃菄菆菈菉菋菍菎菐菑菒菓菕菗菙菚菛菞菢菣菤菦菧菨菫菬菭恰洽牵扦钎铅千迁签仟谦乾黔钱钳前潜遣浅谴堑嵌欠歉枪呛腔羌墙蔷强抢橇锹敲悄桥瞧乔侨巧鞘撬翘峭俏窍切茄且怯窃钦侵亲秦琴勤芹擒禽寝沁青轻氢倾卿清擎晴氰情顷请庆琼穷秋丘邱球求囚酋泅趋区蛆曲躯屈驱渠\"],[\"c840\",\"菮華菳\",4,\"菺菻菼菾菿萀萂萅萇萈萉萊萐萒\",5,\"萙萚萛萞\",5,\"萩\",7,\"萲\",5,\"萹萺萻萾\",7,\"葇葈葉\"],[\"c880\",\"葊\",6,\"葒\",4,\"葘葝葞葟葠葢葤\",4,\"葪葮葯葰葲葴葷葹葻葼取娶龋趣去圈颧权醛泉全痊拳犬券劝缺炔瘸却鹊榷确雀裙群然燃冉染瓤壤攘嚷让饶扰绕惹热壬仁人忍韧任认刃妊纫扔仍日戎茸蓉荣融熔溶容绒冗揉柔肉茹蠕儒孺如辱乳汝入褥软阮蕊瑞锐闰润若弱撒洒萨腮鳃塞赛三叁\"],[\"c940\",\"葽\",4,\"蒃蒄蒅蒆蒊蒍蒏\",7,\"蒘蒚蒛蒝蒞蒟蒠蒢\",12,\"蒰蒱蒳蒵蒶蒷蒻蒼蒾蓀蓂蓃蓅蓆蓇蓈蓋蓌蓎蓏蓒蓔蓕蓗\"],[\"c980\",\"蓘\",4,\"蓞蓡蓢蓤蓧\",4,\"蓭蓮蓯蓱\",10,\"蓽蓾蔀蔁蔂伞散桑嗓丧搔骚扫嫂瑟色涩森僧莎砂杀刹沙纱傻啥煞筛晒珊苫杉山删煽衫闪陕擅赡膳善汕扇缮墒伤商赏晌上尚裳梢捎稍烧芍勺韶少哨邵绍奢赊蛇舌舍赦摄射慑涉社设砷申呻伸身深娠绅神沈审婶甚肾慎渗声生甥牲升绳\"],[\"ca40\",\"蔃\",8,\"蔍蔎蔏蔐蔒蔔蔕蔖蔘蔙蔛蔜蔝蔞蔠蔢\",8,\"蔭\",9,\"蔾\",4,\"蕄蕅蕆蕇蕋\",10],[\"ca80\",\"蕗蕘蕚蕛蕜蕝蕟\",4,\"蕥蕦蕧蕩\",8,\"蕳蕵蕶蕷蕸蕼蕽蕿薀薁省盛剩胜圣师失狮施湿诗尸虱十石拾时什食蚀实识史矢使屎驶始式示士世柿事拭誓逝势是嗜噬适仕侍释饰氏市恃室视试收手首守寿授售受瘦兽蔬枢梳殊抒输叔舒淑疏书赎孰熟薯暑曙署蜀黍鼠属术述树束戍竖墅庶数漱\"],[\"cb40\",\"薂薃薆薈\",6,\"薐\",10,\"薝\",6,\"薥薦薧薩薫薬薭薱\",5,\"薸薺\",6,\"藂\",6,\"藊\",4,\"藑藒\"],[\"cb80\",\"藔藖\",5,\"藝\",6,\"藥藦藧藨藪\",14,\"恕刷耍摔衰甩帅栓拴霜双爽谁水睡税吮瞬顺舜说硕朔烁斯撕嘶思私司丝死肆寺嗣四伺似饲巳松耸怂颂送宋讼诵搜艘擞嗽苏酥俗素速粟僳塑溯宿诉肃酸蒜算虽隋随绥髓碎岁穗遂隧祟孙损笋蓑梭唆缩琐索锁所塌他它她塔\"],[\"cc40\",\"藹藺藼藽藾蘀\",4,\"蘆\",10,\"蘒蘓蘔蘕蘗\",15,\"蘨蘪\",13,\"蘹蘺蘻蘽蘾蘿虀\"],[\"cc80\",\"虁\",11,\"虒虓處\",4,\"虛虜虝號虠虡虣\",7,\"獭挞蹋踏胎苔抬台泰酞太态汰坍摊贪瘫滩坛檀痰潭谭谈坦毯袒碳探叹炭汤塘搪堂棠膛唐糖倘躺淌趟烫掏涛滔绦萄桃逃淘陶讨套特藤腾疼誊梯剔踢锑提题蹄啼体替嚏惕涕剃屉天添填田甜恬舔腆挑条迢眺跳贴铁帖厅听烃\"],[\"cd40\",\"虭虯虰虲\",6,\"蚃\",6,\"蚎\",4,\"蚔蚖\",5,\"蚞\",4,\"蚥蚦蚫蚭蚮蚲蚳蚷蚸蚹蚻\",4,\"蛁蛂蛃蛅蛈蛌蛍蛒蛓蛕蛖蛗蛚蛜\"],[\"cd80\",\"蛝蛠蛡蛢蛣蛥蛦蛧蛨蛪蛫蛬蛯蛵蛶蛷蛺蛻蛼蛽蛿蜁蜄蜅蜆蜋蜌蜎蜏蜐蜑蜔蜖汀廷停亭庭挺艇通桐酮瞳同铜彤童桶捅筒统痛偷投头透凸秃突图徒途涂屠土吐兔湍团推颓腿蜕褪退吞屯臀拖托脱鸵陀驮驼椭妥拓唾挖哇蛙洼娃瓦袜歪外豌弯湾玩顽丸烷完碗挽晚皖惋宛婉万腕汪王亡枉网往旺望忘妄威\"],[\"ce40\",\"蜙蜛蜝蜟蜠蜤蜦蜧蜨蜪蜫蜬蜭蜯蜰蜲蜳蜵蜶蜸蜹蜺蜼蜽蝀\",6,\"蝊蝋蝍蝏蝐蝑蝒蝔蝕蝖蝘蝚\",5,\"蝡蝢蝦\",7,\"蝯蝱蝲蝳蝵\"],[\"ce80\",\"蝷蝸蝹蝺蝿螀螁螄螆螇螉螊螌螎\",4,\"螔螕螖螘\",6,\"螠\",4,\"巍微危韦违桅围唯惟为潍维苇萎委伟伪尾纬未蔚味畏胃喂魏位渭谓尉慰卫瘟温蚊文闻纹吻稳紊问嗡翁瓮挝蜗涡窝我斡卧握沃巫呜钨乌污诬屋无芜梧吾吴毋武五捂午舞伍侮坞戊雾晤物勿务悟误昔熙析西硒矽晰嘻吸锡牺\"],[\"cf40\",\"螥螦螧螩螪螮螰螱螲螴螶螷螸螹螻螼螾螿蟁\",4,\"蟇蟈蟉蟌\",4,\"蟔\",6,\"蟜蟝蟞蟟蟡蟢蟣蟤蟦蟧蟨蟩蟫蟬蟭蟯\",9],[\"cf80\",\"蟺蟻蟼蟽蟿蠀蠁蠂蠄\",5,\"蠋\",7,\"蠔蠗蠘蠙蠚蠜\",4,\"蠣稀息希悉膝夕惜熄烯溪汐犀檄袭席习媳喜铣洗系隙戏细瞎虾匣霞辖暇峡侠狭下厦夏吓掀锨先仙鲜纤咸贤衔舷闲涎弦嫌显险现献县腺馅羡宪陷限线相厢镶香箱襄湘乡翔祥详想响享项巷橡像向象萧硝霄削哮嚣销消宵淆晓\"],[\"d040\",\"蠤\",13,\"蠳\",5,\"蠺蠻蠽蠾蠿衁衂衃衆\",5,\"衎\",5,\"衕衖衘衚\",6,\"衦衧衪衭衯衱衳衴衵衶衸衹衺\"],[\"d080\",\"衻衼袀袃袆袇袉袊袌袎袏袐袑袓袔袕袗\",4,\"袝\",4,\"袣袥\",5,\"小孝校肖啸笑效楔些歇蝎鞋协挟携邪斜胁谐写械卸蟹懈泄泻谢屑薪芯锌欣辛新忻心信衅星腥猩惺兴刑型形邢行醒幸杏性姓兄凶胸匈汹雄熊休修羞朽嗅锈秀袖绣墟戌需虚嘘须徐许蓄酗叙旭序畜恤絮婿绪续轩喧宣悬旋玄\"],[\"d140\",\"袬袮袯袰袲\",4,\"袸袹袺袻袽袾袿裀裃裄裇裈裊裋裌裍裏裐裑裓裖裗裚\",4,\"裠裡裦裧裩\",6,\"裲裵裶裷裺裻製裿褀褁褃\",5],[\"d180\",\"褉褋\",4,\"褑褔\",4,\"褜\",4,\"褢褣褤褦褧褨褩褬褭褮褯褱褲褳褵褷选癣眩绚靴薛学穴雪血勋熏循旬询寻驯巡殉汛训讯逊迅压押鸦鸭呀丫芽牙蚜崖衙涯雅哑亚讶焉咽阉烟淹盐严研蜒岩延言颜阎炎沿奄掩眼衍演艳堰燕厌砚雁唁彦焰宴谚验殃央鸯秧杨扬佯疡羊洋阳氧仰痒养样漾邀腰妖瑶\"],[\"d240\",\"褸\",8,\"襂襃襅\",24,\"襠\",5,\"襧\",19,\"襼\"],[\"d280\",\"襽襾覀覂覄覅覇\",26,\"摇尧遥窑谣姚咬舀药要耀椰噎耶爷野冶也页掖业叶曳腋夜液一壹医揖铱依伊衣颐夷遗移仪胰疑沂宜姨彝椅蚁倚已乙矣以艺抑易邑屹亿役臆逸肄疫亦裔意毅忆义益溢诣议谊译异翼翌绎茵荫因殷音阴姻吟银淫寅饮尹引隐\"],[\"d340\",\"覢\",30,\"觃觍觓觔觕觗觘觙觛觝觟觠觡觢觤觧觨觩觪觬觭觮觰觱觲觴\",6],[\"d380\",\"觻\",4,\"訁\",5,\"計\",21,\"印英樱婴鹰应缨莹萤营荧蝇迎赢盈影颖硬映哟拥佣臃痈庸雍踊蛹咏泳涌永恿勇用幽优悠忧尤由邮铀犹油游酉有友右佑釉诱又幼迂淤于盂榆虞愚舆余俞逾鱼愉渝渔隅予娱雨与屿禹宇语羽玉域芋郁吁遇喻峪御愈欲狱育誉\"],[\"d440\",\"訞\",31,\"訿\",8,\"詉\",21],[\"d480\",\"詟\",25,\"詺\",6,\"浴寓裕预豫驭鸳渊冤元垣袁原援辕园员圆猿源缘远苑愿怨院曰约越跃钥岳粤月悦阅耘云郧匀陨允运蕴酝晕韵孕匝砸杂栽哉灾宰载再在咱攒暂赞赃脏葬遭糟凿藻枣早澡蚤躁噪造皂灶燥责择则泽贼怎增憎曾赠扎喳渣札轧\"],[\"d540\",\"誁\",7,\"誋\",7,\"誔\",46],[\"d580\",\"諃\",32,\"铡闸眨栅榨咋乍炸诈摘斋宅窄债寨瞻毡詹粘沾盏斩辗崭展蘸栈占战站湛绽樟章彰漳张掌涨杖丈帐账仗胀瘴障招昭找沼赵照罩兆肇召遮折哲蛰辙者锗蔗这浙珍斟真甄砧臻贞针侦枕疹诊震振镇阵蒸挣睁征狰争怔整拯正政\"],[\"d640\",\"諤\",34,\"謈\",27],[\"d680\",\"謤謥謧\",30,\"帧症郑证芝枝支吱蜘知肢脂汁之织职直植殖执值侄址指止趾只旨纸志挚掷至致置帜峙制智秩稚质炙痔滞治窒中盅忠钟衷终种肿重仲众舟周州洲诌粥轴肘帚咒皱宙昼骤珠株蛛朱猪诸诛逐竹烛煮拄瞩嘱主著柱助蛀贮铸筑\"],[\"d740\",\"譆\",31,\"譧\",4,\"譭\",25],[\"d780\",\"讇\",24,\"讬讱讻诇诐诪谉谞住注祝驻抓爪拽专砖转撰赚篆桩庄装妆撞壮状椎锥追赘坠缀谆准捉拙卓桌琢茁酌啄着灼浊兹咨资姿滋淄孜紫仔籽滓子自渍字鬃棕踪宗综总纵邹走奏揍租足卒族祖诅阻组钻纂嘴醉最罪尊遵昨左佐柞做作坐座\"],[\"d840\",\"谸\",8,\"豂豃豄豅豈豊豋豍\",7,\"豖豗豘豙豛\",5,\"豣\",6,\"豬\",6,\"豴豵豶豷豻\",6,\"貃貄貆貇\"],[\"d880\",\"貈貋貍\",6,\"貕貖貗貙\",20,\"亍丌兀丐廿卅丕亘丞鬲孬噩丨禺丿匕乇夭爻卮氐囟胤馗毓睾鼗丶亟鼐乜乩亓芈孛啬嘏仄厍厝厣厥厮靥赝匚叵匦匮匾赜卦卣刂刈刎刭刳刿剀剌剞剡剜蒯剽劂劁劐劓冂罔亻仃仉仂仨仡仫仞伛仳伢佤仵伥伧伉伫佞佧攸佚佝\"],[\"d940\",\"貮\",62],[\"d980\",\"賭\",32,\"佟佗伲伽佶佴侑侉侃侏佾佻侪佼侬侔俦俨俪俅俚俣俜俑俟俸倩偌俳倬倏倮倭俾倜倌倥倨偾偃偕偈偎偬偻傥傧傩傺僖儆僭僬僦僮儇儋仝氽佘佥俎龠汆籴兮巽黉馘冁夔勹匍訇匐凫夙兕亠兖亳衮袤亵脔裒禀嬴蠃羸冫冱冽冼\"],[\"da40\",\"贎\",14,\"贠赑赒赗赟赥赨赩赪赬赮赯赱赲赸\",8,\"趂趃趆趇趈趉趌\",4,\"趒趓趕\",9,\"趠趡\"],[\"da80\",\"趢趤\",12,\"趲趶趷趹趻趽跀跁跂跅跇跈跉跊跍跐跒跓跔凇冖冢冥讠讦讧讪讴讵讷诂诃诋诏诎诒诓诔诖诘诙诜诟诠诤诨诩诮诰诳诶诹诼诿谀谂谄谇谌谏谑谒谔谕谖谙谛谘谝谟谠谡谥谧谪谫谮谯谲谳谵谶卩卺阝阢阡阱阪阽阼陂陉陔陟陧陬陲陴隈隍隗隰邗邛邝邙邬邡邴邳邶邺\"],[\"db40\",\"跕跘跙跜跠跡跢跥跦跧跩跭跮跰跱跲跴跶跼跾\",6,\"踆踇踈踋踍踎踐踑踒踓踕\",7,\"踠踡踤\",4,\"踫踭踰踲踳踴踶踷踸踻踼踾\"],[\"db80\",\"踿蹃蹅蹆蹌\",4,\"蹓\",5,\"蹚\",11,\"蹧蹨蹪蹫蹮蹱邸邰郏郅邾郐郄郇郓郦郢郜郗郛郫郯郾鄄鄢鄞鄣鄱鄯鄹酃酆刍奂劢劬劭劾哿勐勖勰叟燮矍廴凵凼鬯厶弁畚巯坌垩垡塾墼壅壑圩圬圪圳圹圮圯坜圻坂坩垅坫垆坼坻坨坭坶坳垭垤垌垲埏垧垴垓垠埕埘埚埙埒垸埴埯埸埤埝\"],[\"dc40\",\"蹳蹵蹷\",4,\"蹽蹾躀躂躃躄躆躈\",6,\"躑躒躓躕\",6,\"躝躟\",11,\"躭躮躰躱躳\",6,\"躻\",7],[\"dc80\",\"軃\",10,\"軏\",21,\"堋堍埽埭堀堞堙塄堠塥塬墁墉墚墀馨鼙懿艹艽艿芏芊芨芄芎芑芗芙芫芸芾芰苈苊苣芘芷芮苋苌苁芩芴芡芪芟苄苎芤苡茉苷苤茏茇苜苴苒苘茌苻苓茑茚茆茔茕苠苕茜荑荛荜茈莒茼茴茱莛荞茯荏荇荃荟荀茗荠茭茺茳荦荥\"],[\"dd40\",\"軥\",62],[\"dd80\",\"輤\",32,\"荨茛荩荬荪荭荮莰荸莳莴莠莪莓莜莅荼莶莩荽莸荻莘莞莨莺莼菁萁菥菘堇萘萋菝菽菖萜萸萑萆菔菟萏萃菸菹菪菅菀萦菰菡葜葑葚葙葳蒇蒈葺蒉葸萼葆葩葶蒌蒎萱葭蓁蓍蓐蓦蒽蓓蓊蒿蒺蓠蒡蒹蒴蒗蓥蓣蔌甍蔸蓰蔹蔟蔺\"],[\"de40\",\"轅\",32,\"轪辀辌辒辝辠辡辢辤辥辦辧辪辬辭辮辯農辳辴辵辷辸辺辻込辿迀迃迆\"],[\"de80\",\"迉\",4,\"迏迒迖迗迚迠迡迣迧迬迯迱迲迴迵迶迺迻迼迾迿逇逈逌逎逓逕逘蕖蔻蓿蓼蕙蕈蕨蕤蕞蕺瞢蕃蕲蕻薤薨薇薏蕹薮薜薅薹薷薰藓藁藜藿蘧蘅蘩蘖蘼廾弈夼奁耷奕奚奘匏尢尥尬尴扌扪抟抻拊拚拗拮挢拶挹捋捃掭揶捱捺掎掴捭掬掊捩掮掼揲揸揠揿揄揞揎摒揆掾摅摁搋搛搠搌搦搡摞撄摭撖\"],[\"df40\",\"這逜連逤逥逧\",5,\"逰\",4,\"逷逹逺逽逿遀遃遅遆遈\",4,\"過達違遖遙遚遜\",5,\"遤遦遧適遪遫遬遯\",4,\"遶\",6,\"遾邁\"],[\"df80\",\"還邅邆邇邉邊邌\",4,\"邒邔邖邘邚邜邞邟邠邤邥邧邨邩邫邭邲邷邼邽邿郀摺撷撸撙撺擀擐擗擤擢攉攥攮弋忒甙弑卟叱叽叩叨叻吒吖吆呋呒呓呔呖呃吡呗呙吣吲咂咔呷呱呤咚咛咄呶呦咝哐咭哂咴哒咧咦哓哔呲咣哕咻咿哌哙哚哜咩咪咤哝哏哞唛哧唠哽唔哳唢唣唏唑唧唪啧喏喵啉啭啁啕唿啐唼\"],[\"e040\",\"郂郃郆郈郉郋郌郍郒郔郕郖郘郙郚郞郟郠郣郤郥郩郪郬郮郰郱郲郳郵郶郷郹郺郻郼郿鄀鄁鄃鄅\",19,\"鄚鄛鄜\"],[\"e080\",\"鄝鄟鄠鄡鄤\",10,\"鄰鄲\",6,\"鄺\",8,\"酄唷啖啵啶啷唳唰啜喋嗒喃喱喹喈喁喟啾嗖喑啻嗟喽喾喔喙嗪嗷嗉嘟嗑嗫嗬嗔嗦嗝嗄嗯嗥嗲嗳嗌嗍嗨嗵嗤辔嘞嘈嘌嘁嘤嘣嗾嘀嘧嘭噘嘹噗嘬噍噢噙噜噌噔嚆噤噱噫噻噼嚅嚓嚯囔囗囝囡囵囫囹囿圄圊圉圜帏帙帔帑帱帻帼\"],[\"e140\",\"酅酇酈酑酓酔酕酖酘酙酛酜酟酠酦酧酨酫酭酳酺酻酼醀\",4,\"醆醈醊醎醏醓\",6,\"醜\",5,\"醤\",5,\"醫醬醰醱醲醳醶醷醸醹醻\"],[\"e180\",\"醼\",10,\"釈釋釐釒\",9,\"針\",8,\"帷幄幔幛幞幡岌屺岍岐岖岈岘岙岑岚岜岵岢岽岬岫岱岣峁岷峄峒峤峋峥崂崃崧崦崮崤崞崆崛嵘崾崴崽嵬嵛嵯嵝嵫嵋嵊嵩嵴嶂嶙嶝豳嶷巅彳彷徂徇徉後徕徙徜徨徭徵徼衢彡犭犰犴犷犸狃狁狎狍狒狨狯狩狲狴狷猁狳猃狺\"],[\"e240\",\"釦\",62],[\"e280\",\"鈥\",32,\"狻猗猓猡猊猞猝猕猢猹猥猬猸猱獐獍獗獠獬獯獾舛夥飧夤夂饣饧\",5,\"饴饷饽馀馄馇馊馍馐馑馓馔馕庀庑庋庖庥庠庹庵庾庳赓廒廑廛廨廪膺忄忉忖忏怃忮怄忡忤忾怅怆忪忭忸怙怵怦怛怏怍怩怫怊怿怡恸恹恻恺恂\"],[\"e340\",\"鉆\",45,\"鉵\",16],[\"e380\",\"銆\",7,\"銏\",24,\"恪恽悖悚悭悝悃悒悌悛惬悻悱惝惘惆惚悴愠愦愕愣惴愀愎愫慊慵憬憔憧憷懔懵忝隳闩闫闱闳闵闶闼闾阃阄阆阈阊阋阌阍阏阒阕阖阗阙阚丬爿戕氵汔汜汊沣沅沐沔沌汨汩汴汶沆沩泐泔沭泷泸泱泗沲泠泖泺泫泮沱泓泯泾\"],[\"e440\",\"銨\",5,\"銯\",24,\"鋉\",31],[\"e480\",\"鋩\",32,\"洹洧洌浃浈洇洄洙洎洫浍洮洵洚浏浒浔洳涑浯涞涠浞涓涔浜浠浼浣渚淇淅淞渎涿淠渑淦淝淙渖涫渌涮渫湮湎湫溲湟溆湓湔渲渥湄滟溱溘滠漭滢溥溧溽溻溷滗溴滏溏滂溟潢潆潇漤漕滹漯漶潋潴漪漉漩澉澍澌潸潲潼潺濑\"],[\"e540\",\"錊\",51,\"錿\",10],[\"e580\",\"鍊\",31,\"鍫濉澧澹澶濂濡濮濞濠濯瀚瀣瀛瀹瀵灏灞宀宄宕宓宥宸甯骞搴寤寮褰寰蹇謇辶迓迕迥迮迤迩迦迳迨逅逄逋逦逑逍逖逡逵逶逭逯遄遑遒遐遨遘遢遛暹遴遽邂邈邃邋彐彗彖彘尻咫屐屙孱屣屦羼弪弩弭艴弼鬻屮妁妃妍妩妪妣\"],[\"e640\",\"鍬\",34,\"鎐\",27],[\"e680\",\"鎬\",29,\"鏋鏌鏍妗姊妫妞妤姒妲妯姗妾娅娆姝娈姣姘姹娌娉娲娴娑娣娓婀婧婊婕娼婢婵胬媪媛婷婺媾嫫媲嫒嫔媸嫠嫣嫱嫖嫦嫘嫜嬉嬗嬖嬲嬷孀尕尜孚孥孳孑孓孢驵驷驸驺驿驽骀骁骅骈骊骐骒骓骖骘骛骜骝骟骠骢骣骥骧纟纡纣纥纨纩\"],[\"e740\",\"鏎\",7,\"鏗\",54],[\"e780\",\"鐎\",32,\"纭纰纾绀绁绂绉绋绌绐绔绗绛绠绡绨绫绮绯绱绲缍绶绺绻绾缁缂缃缇缈缋缌缏缑缒缗缙缜缛缟缡\",6,\"缪缫缬缭缯\",4,\"缵幺畿巛甾邕玎玑玮玢玟珏珂珑玷玳珀珉珈珥珙顼琊珩珧珞玺珲琏琪瑛琦琥琨琰琮琬\"],[\"e840\",\"鐯\",14,\"鐿\",43,\"鑬鑭鑮鑯\"],[\"e880\",\"鑰\",20,\"钑钖钘铇铏铓铔铚铦铻锜锠琛琚瑁瑜瑗瑕瑙瑷瑭瑾璜璎璀璁璇璋璞璨璩璐璧瓒璺韪韫韬杌杓杞杈杩枥枇杪杳枘枧杵枨枞枭枋杷杼柰栉柘栊柩枰栌柙枵柚枳柝栀柃枸柢栎柁柽栲栳桠桡桎桢桄桤梃栝桕桦桁桧桀栾桊桉栩梵梏桴桷梓桫棂楮棼椟椠棹\"],[\"e940\",\"锧锳锽镃镈镋镕镚镠镮镴镵長\",7,\"門\",42],[\"e980\",\"閫\",32,\"椤棰椋椁楗棣椐楱椹楠楂楝榄楫榀榘楸椴槌榇榈槎榉楦楣楹榛榧榻榫榭槔榱槁槊槟榕槠榍槿樯槭樗樘橥槲橄樾檠橐橛樵檎橹樽樨橘橼檑檐檩檗檫猷獒殁殂殇殄殒殓殍殚殛殡殪轫轭轱轲轳轵轶轸轷轹轺轼轾辁辂辄辇辋\"],[\"ea40\",\"闌\",27,\"闬闿阇阓阘阛阞阠阣\",6,\"阫阬阭阯阰阷阸阹阺阾陁陃陊陎陏陑陒陓陖陗\"],[\"ea80\",\"陘陙陚陜陝陞陠陣陥陦陫陭\",4,\"陳陸\",12,\"隇隉隊辍辎辏辘辚軎戋戗戛戟戢戡戥戤戬臧瓯瓴瓿甏甑甓攴旮旯旰昊昙杲昃昕昀炅曷昝昴昱昶昵耆晟晔晁晏晖晡晗晷暄暌暧暝暾曛曜曦曩贲贳贶贻贽赀赅赆赈赉赇赍赕赙觇觊觋觌觎觏觐觑牮犟牝牦牯牾牿犄犋犍犏犒挈挲掰\"],[\"eb40\",\"隌階隑隒隓隕隖隚際隝\",9,\"隨\",7,\"隱隲隴隵隷隸隺隻隿雂雃雈雊雋雐雑雓雔雖\",9,\"雡\",6,\"雫\"],[\"eb80\",\"雬雭雮雰雱雲雴雵雸雺電雼雽雿霂霃霅霊霋霌霐霑霒霔霕霗\",4,\"霝霟霠搿擘耄毪毳毽毵毹氅氇氆氍氕氘氙氚氡氩氤氪氲攵敕敫牍牒牖爰虢刖肟肜肓肼朊肽肱肫肭肴肷胧胨胩胪胛胂胄胙胍胗朐胝胫胱胴胭脍脎胲胼朕脒豚脶脞脬脘脲腈腌腓腴腙腚腱腠腩腼腽腭腧塍媵膈膂膑滕膣膪臌朦臊膻\"],[\"ec40\",\"霡\",8,\"霫霬霮霯霱霳\",4,\"霺霻霼霽霿\",18,\"靔靕靗靘靚靜靝靟靣靤靦靧靨靪\",7],[\"ec80\",\"靲靵靷\",4,\"靽\",7,\"鞆\",4,\"鞌鞎鞏鞐鞓鞕鞖鞗鞙\",4,\"臁膦欤欷欹歃歆歙飑飒飓飕飙飚殳彀毂觳斐齑斓於旆旄旃旌旎旒旖炀炜炖炝炻烀炷炫炱烨烊焐焓焖焯焱煳煜煨煅煲煊煸煺熘熳熵熨熠燠燔燧燹爝爨灬焘煦熹戾戽扃扈扉礻祀祆祉祛祜祓祚祢祗祠祯祧祺禅禊禚禧禳忑忐\"],[\"ed40\",\"鞞鞟鞡鞢鞤\",6,\"鞬鞮鞰鞱鞳鞵\",46],[\"ed80\",\"韤韥韨韮\",4,\"韴韷\",23,\"怼恝恚恧恁恙恣悫愆愍慝憩憝懋懑戆肀聿沓泶淼矶矸砀砉砗砘砑斫砭砜砝砹砺砻砟砼砥砬砣砩硎硭硖硗砦硐硇硌硪碛碓碚碇碜碡碣碲碹碥磔磙磉磬磲礅磴礓礤礞礴龛黹黻黼盱眄眍盹眇眈眚眢眙眭眦眵眸睐睑睇睃睚睨\"],[\"ee40\",\"頏\",62],[\"ee80\",\"顎\",32,\"睢睥睿瞍睽瞀瞌瞑瞟瞠瞰瞵瞽町畀畎畋畈畛畲畹疃罘罡罟詈罨罴罱罹羁罾盍盥蠲钅钆钇钋钊钌钍钏钐钔钗钕钚钛钜钣钤钫钪钭钬钯钰钲钴钶\",4,\"钼钽钿铄铈\",6,\"铐铑铒铕铖铗铙铘铛铞铟铠铢铤铥铧铨铪\"],[\"ef40\",\"顯\",5,\"颋颎颒颕颙颣風\",37,\"飏飐飔飖飗飛飜飝飠\",4],[\"ef80\",\"飥飦飩\",30,\"铩铫铮铯铳铴铵铷铹铼铽铿锃锂锆锇锉锊锍锎锏锒\",4,\"锘锛锝锞锟锢锪锫锩锬锱锲锴锶锷锸锼锾锿镂锵镄镅镆镉镌镎镏镒镓镔镖镗镘镙镛镞镟镝镡镢镤\",8,\"镯镱镲镳锺矧矬雉秕秭秣秫稆嵇稃稂稞稔\"],[\"f040\",\"餈\",4,\"餎餏餑\",28,\"餯\",26],[\"f080\",\"饊\",9,\"饖\",12,\"饤饦饳饸饹饻饾馂馃馉稹稷穑黏馥穰皈皎皓皙皤瓞瓠甬鸠鸢鸨\",4,\"鸲鸱鸶鸸鸷鸹鸺鸾鹁鹂鹄鹆鹇鹈鹉鹋鹌鹎鹑鹕鹗鹚鹛鹜鹞鹣鹦\",6,\"鹱鹭鹳疒疔疖疠疝疬疣疳疴疸痄疱疰痃痂痖痍痣痨痦痤痫痧瘃痱痼痿瘐瘀瘅瘌瘗瘊瘥瘘瘕瘙\"],[\"f140\",\"馌馎馚\",10,\"馦馧馩\",47],[\"f180\",\"駙\",32,\"瘛瘼瘢瘠癀瘭瘰瘿瘵癃瘾瘳癍癞癔癜癖癫癯翊竦穸穹窀窆窈窕窦窠窬窨窭窳衤衩衲衽衿袂袢裆袷袼裉裢裎裣裥裱褚裼裨裾裰褡褙褓褛褊褴褫褶襁襦襻疋胥皲皴矜耒耔耖耜耠耢耥耦耧耩耨耱耋耵聃聆聍聒聩聱覃顸颀颃\"],[\"f240\",\"駺\",62],[\"f280\",\"騹\",32,\"颉颌颍颏颔颚颛颞颟颡颢颥颦虍虔虬虮虿虺虼虻蚨蚍蚋蚬蚝蚧蚣蚪蚓蚩蚶蛄蚵蛎蚰蚺蚱蚯蛉蛏蚴蛩蛱蛲蛭蛳蛐蜓蛞蛴蛟蛘蛑蜃蜇蛸蜈蜊蜍蜉蜣蜻蜞蜥蜮蜚蜾蝈蜴蜱蜩蜷蜿螂蜢蝽蝾蝻蝠蝰蝌蝮螋蝓蝣蝼蝤蝙蝥螓螯螨蟒\"],[\"f340\",\"驚\",17,\"驲骃骉骍骎骔骕骙骦骩\",6,\"骲骳骴骵骹骻骽骾骿髃髄髆\",4,\"髍髎髏髐髒體髕髖髗髙髚髛髜\"],[\"f380\",\"髝髞髠髢髣髤髥髧髨髩髪髬髮髰\",8,\"髺髼\",6,\"鬄鬅鬆蟆螈螅螭螗螃螫蟥螬螵螳蟋蟓螽蟑蟀蟊蟛蟪蟠蟮蠖蠓蟾蠊蠛蠡蠹蠼缶罂罄罅舐竺竽笈笃笄笕笊笫笏筇笸笪笙笮笱笠笥笤笳笾笞筘筚筅筵筌筝筠筮筻筢筲筱箐箦箧箸箬箝箨箅箪箜箢箫箴篑篁篌篝篚篥篦篪簌篾篼簏簖簋\"],[\"f440\",\"鬇鬉\",5,\"鬐鬑鬒鬔\",10,\"鬠鬡鬢鬤\",10,\"鬰鬱鬳\",7,\"鬽鬾鬿魀魆魊魋魌魎魐魒魓魕\",5],[\"f480\",\"魛\",32,\"簟簪簦簸籁籀臾舁舂舄臬衄舡舢舣舭舯舨舫舸舻舳舴舾艄艉艋艏艚艟艨衾袅袈裘裟襞羝羟羧羯羰羲籼敉粑粝粜粞粢粲粼粽糁糇糌糍糈糅糗糨艮暨羿翎翕翥翡翦翩翮翳糸絷綦綮繇纛麸麴赳趄趔趑趱赧赭豇豉酊酐酎酏酤\"],[\"f540\",\"魼\",62],[\"f580\",\"鮻\",32,\"酢酡酰酩酯酽酾酲酴酹醌醅醐醍醑醢醣醪醭醮醯醵醴醺豕鹾趸跫踅蹙蹩趵趿趼趺跄跖跗跚跞跎跏跛跆跬跷跸跣跹跻跤踉跽踔踝踟踬踮踣踯踺蹀踹踵踽踱蹉蹁蹂蹑蹒蹊蹰蹶蹼蹯蹴躅躏躔躐躜躞豸貂貊貅貘貔斛觖觞觚觜\"],[\"f640\",\"鯜\",62],[\"f680\",\"鰛\",32,\"觥觫觯訾謦靓雩雳雯霆霁霈霏霎霪霭霰霾龀龃龅\",5,\"龌黾鼋鼍隹隼隽雎雒瞿雠銎銮鋈錾鍪鏊鎏鐾鑫鱿鲂鲅鲆鲇鲈稣鲋鲎鲐鲑鲒鲔鲕鲚鲛鲞\",5,\"鲥\",4,\"鲫鲭鲮鲰\",7,\"鲺鲻鲼鲽鳄鳅鳆鳇鳊鳋\"],[\"f740\",\"鰼\",62],[\"f780\",\"鱻鱽鱾鲀鲃鲄鲉鲊鲌鲏鲓鲖鲗鲘鲙鲝鲪鲬鲯鲹鲾\",4,\"鳈鳉鳑鳒鳚鳛鳠鳡鳌\",4,\"鳓鳔鳕鳗鳘鳙鳜鳝鳟鳢靼鞅鞑鞒鞔鞯鞫鞣鞲鞴骱骰骷鹘骶骺骼髁髀髅髂髋髌髑魅魃魇魉魈魍魑飨餍餮饕饔髟髡髦髯髫髻髭髹鬈鬏鬓鬟鬣麽麾縻麂麇麈麋麒鏖麝麟黛黜黝黠黟黢黩黧黥黪黯鼢鼬鼯鼹鼷鼽鼾齄\"],[\"f840\",\"鳣\",62],[\"f880\",\"鴢\",32],[\"f940\",\"鵃\",62],[\"f980\",\"鶂\",32],[\"fa40\",\"鶣\",62],[\"fa80\",\"鷢\",32],[\"fb40\",\"鸃\",27,\"鸤鸧鸮鸰鸴鸻鸼鹀鹍鹐鹒鹓鹔鹖鹙鹝鹟鹠鹡鹢鹥鹮鹯鹲鹴\",9,\"麀\"],[\"fb80\",\"麁麃麄麅麆麉麊麌\",5,\"麔\",8,\"麞麠\",5,\"麧麨麩麪\"],[\"fc40\",\"麫\",8,\"麵麶麷麹麺麼麿\",4,\"黅黆黇黈黊黋黌黐黒黓黕黖黗黙黚點黡黣黤黦黨黫黬黭黮黰\",8,\"黺黽黿\",6],[\"fc80\",\"鼆\",4,\"鼌鼏鼑鼒鼔鼕鼖鼘鼚\",5,\"鼡鼣\",8,\"鼭鼮鼰鼱\"],[\"fd40\",\"鼲\",4,\"鼸鼺鼼鼿\",4,\"齅\",10,\"齒\",38],[\"fd80\",\"齹\",5,\"龁龂龍\",11,\"龜龝龞龡\",4,\"郎凉秊裏隣\"],[\"fe40\",\"兀嗀﨎﨏﨑﨓﨔礼﨟蘒﨡﨣﨤﨧﨨﨩\"]]");

/***/ }),
/* 172 */
/***/ (function(module, exports, __webpack_require__) {

// fallback for non-array-like ES3 and non-enumerable old V8 strings
var cof = __webpack_require__(99);
// eslint-disable-next-line no-prototype-builtins
module.exports = Object('z').propertyIsEnumerable(0) ? Object : function (it) {
  return cof(it) == 'String' ? it.split('') : Object(it);
};


/***/ }),
/* 173 */
/***/ (function(module, exports) {

// 7.2.1 RequireObjectCoercible(argument)
module.exports = function (it) {
  if (it == undefined) throw TypeError("Can't call method on  " + it);
  return it;
};


/***/ }),
/* 174 */
/***/ (function(module, exports, __webpack_require__) {

var pIE = __webpack_require__(128);
var createDesc = __webpack_require__(100);
var toIObject = __webpack_require__(50);
var toPrimitive = __webpack_require__(175);
var has = __webpack_require__(51);
var IE8_DOM_DEFINE = __webpack_require__(254);
var gOPD = Object.getOwnPropertyDescriptor;

exports.f = __webpack_require__(23) ? gOPD : function getOwnPropertyDescriptor(O, P) {
  O = toIObject(O);
  P = toPrimitive(P, true);
  if (IE8_DOM_DEFINE) try {
    return gOPD(O, P);
  } catch (e) { /* empty */ }
  if (has(O, P)) return createDesc(!pIE.f.call(O, P), O[P]);
};


/***/ }),
/* 175 */
/***/ (function(module, exports, __webpack_require__) {

// 7.1.1 ToPrimitive(input [, PreferredType])
var isObject = __webpack_require__(22);
// instead of the ES6 spec version, we didn't implement @@toPrimitive case
// and the second argument - flag - preferred type is a string
module.exports = function (it, S) {
  if (!isObject(it)) return it;
  var fn, val;
  if (S && typeof (fn = it.toString) == 'function' && !isObject(val = fn.call(it))) return val;
  if (typeof (fn = it.valueOf) == 'function' && !isObject(val = fn.call(it))) return val;
  if (!S && typeof (fn = it.toString) == 'function' && !isObject(val = fn.call(it))) return val;
  throw TypeError("Can't convert object to primitive value");
};


/***/ }),
/* 176 */
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__(22);
var document = __webpack_require__(17).document;
// typeof document.createElement is 'object' in old IE
var is = isObject(document) && isObject(document.createElement);
module.exports = function (it) {
  return is ? document.createElement(it) : {};
};


/***/ }),
/* 177 */
/***/ (function(module, exports, __webpack_require__) {

// most Object methods by ES6 should accept primitives
var $export = __webpack_require__(9);
var core = __webpack_require__(7);
var fails = __webpack_require__(52);
module.exports = function (KEY, exec) {
  var fn = (core.Object || {})[KEY] || Object[KEY];
  var exp = {};
  exp[KEY] = exec(fn);
  $export($export.S + $export.F * fails(function () { fn(1); }), 'Object', exp);
};


/***/ }),
/* 178 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var LIBRARY = __webpack_require__(104);
var $export = __webpack_require__(9);
var redefine = __webpack_require__(256);
var hide = __webpack_require__(41);
var Iterators = __webpack_require__(103);
var $iterCreate = __webpack_require__(424);
var setToStringTag = __webpack_require__(106);
var getPrototypeOf = __webpack_require__(426);
var ITERATOR = __webpack_require__(21)('iterator');
var BUGGY = !([].keys && 'next' in [].keys()); // Safari has buggy iterators w/o `next`
var FF_ITERATOR = '@@iterator';
var KEYS = 'keys';
var VALUES = 'values';

var returnThis = function () { return this; };

module.exports = function (Base, NAME, Constructor, next, DEFAULT, IS_SET, FORCED) {
  $iterCreate(Constructor, NAME, next);
  var getMethod = function (kind) {
    if (!BUGGY && kind in proto) return proto[kind];
    switch (kind) {
      case KEYS: return function keys() { return new Constructor(this, kind); };
      case VALUES: return function values() { return new Constructor(this, kind); };
    } return function entries() { return new Constructor(this, kind); };
  };
  var TAG = NAME + ' Iterator';
  var DEF_VALUES = DEFAULT == VALUES;
  var VALUES_BUG = false;
  var proto = Base.prototype;
  var $native = proto[ITERATOR] || proto[FF_ITERATOR] || DEFAULT && proto[DEFAULT];
  var $default = $native || getMethod(DEFAULT);
  var $entries = DEFAULT ? !DEF_VALUES ? $default : getMethod('entries') : undefined;
  var $anyNative = NAME == 'Array' ? proto.entries || $native : $native;
  var methods, key, IteratorPrototype;
  // Fix native
  if ($anyNative) {
    IteratorPrototype = getPrototypeOf($anyNative.call(new Base()));
    if (IteratorPrototype !== Object.prototype && IteratorPrototype.next) {
      // Set @@toStringTag to native iterators
      setToStringTag(IteratorPrototype, TAG, true);
      // fix for some old engines
      if (!LIBRARY && typeof IteratorPrototype[ITERATOR] != 'function') hide(IteratorPrototype, ITERATOR, returnThis);
    }
  }
  // fix Array#{values, @@iterator}.name in V8 / FF
  if (DEF_VALUES && $native && $native.name !== VALUES) {
    VALUES_BUG = true;
    $default = function values() { return $native.call(this); };
  }
  // Define iterator
  if ((!LIBRARY || FORCED) && (BUGGY || VALUES_BUG || !proto[ITERATOR])) {
    hide(proto, ITERATOR, $default);
  }
  // Plug for library
  Iterators[NAME] = $default;
  Iterators[TAG] = returnThis;
  if (DEFAULT) {
    methods = {
      values: DEF_VALUES ? $default : getMethod(VALUES),
      keys: IS_SET ? $default : getMethod(KEYS),
      entries: $entries
    };
    if (FORCED) for (key in methods) {
      if (!(key in proto)) redefine(proto, key, methods[key]);
    } else $export($export.P + $export.F * (BUGGY || VALUES_BUG), NAME, methods);
  }
  return methods;
};


/***/ }),
/* 179 */
/***/ (function(module, exports) {

// 7.1.4 ToInteger
var ceil = Math.ceil;
var floor = Math.floor;
module.exports = function (it) {
  return isNaN(it = +it) ? 0 : (it > 0 ? floor : ceil)(it);
};


/***/ }),
/* 180 */
/***/ (function(module, exports, __webpack_require__) {

var shared = __webpack_require__(181)('keys');
var uid = __webpack_require__(131);
module.exports = function (key) {
  return shared[key] || (shared[key] = uid(key));
};


/***/ }),
/* 181 */
/***/ (function(module, exports, __webpack_require__) {

var core = __webpack_require__(7);
var global = __webpack_require__(17);
var SHARED = '__core-js_shared__';
var store = global[SHARED] || (global[SHARED] = {});

(module.exports = function (key, value) {
  return store[key] || (store[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: core.version,
  mode: __webpack_require__(104) ? 'pure' : 'global',
  copyright: '© 2019 Denis Pushkarev (zloirock.ru)'
});


/***/ }),
/* 182 */
/***/ (function(module, exports) {

// IE 8- don't enum bug keys
module.exports = (
  'constructor,hasOwnProperty,isPrototypeOf,propertyIsEnumerable,toLocaleString,toString,valueOf'
).split(',');


/***/ }),
/* 183 */
/***/ (function(module, exports, __webpack_require__) {

var classof = __webpack_require__(184);
var ITERATOR = __webpack_require__(21)('iterator');
var Iterators = __webpack_require__(103);
module.exports = __webpack_require__(7).getIteratorMethod = function (it) {
  if (it != undefined) return it[ITERATOR]
    || it['@@iterator']
    || Iterators[classof(it)];
};


/***/ }),
/* 184 */
/***/ (function(module, exports, __webpack_require__) {

// getting tag from 19.1.3.6 Object.prototype.toString()
var cof = __webpack_require__(99);
var TAG = __webpack_require__(21)('toStringTag');
// ES3 wrong here
var ARG = cof(function () { return arguments; }()) == 'Arguments';

// fallback for IE11 Script Access Denied error
var tryGet = function (it, key) {
  try {
    return it[key];
  } catch (e) { /* empty */ }
};

module.exports = function (it) {
  var O, T, B;
  return it === undefined ? 'Undefined' : it === null ? 'Null'
    // @@toStringTag case
    : typeof (T = tryGet(O = Object(it), TAG)) == 'string' ? T
    // builtinTag case
    : ARG ? cof(O)
    // ES3 arguments fallback
    : (B = cof(O)) == 'Object' && typeof O.callee == 'function' ? 'Arguments' : B;
};


/***/ }),
/* 185 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


exports.__esModule = true;

var _iterator = __webpack_require__(432);

var _iterator2 = _interopRequireDefault(_iterator);

var _symbol = __webpack_require__(434);

var _symbol2 = _interopRequireDefault(_symbol);

var _typeof = typeof _symbol2.default === "function" && typeof _iterator2.default === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof _symbol2.default === "function" && obj.constructor === _symbol2.default && obj !== _symbol2.default.prototype ? "symbol" : typeof obj; };

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

exports.default = typeof _symbol2.default === "function" && _typeof(_iterator2.default) === "symbol" ? function (obj) {
  return typeof obj === "undefined" ? "undefined" : _typeof(obj);
} : function (obj) {
  return obj && typeof _symbol2.default === "function" && obj.constructor === _symbol2.default && obj !== _symbol2.default.prototype ? "symbol" : typeof obj === "undefined" ? "undefined" : _typeof(obj);
};

/***/ }),
/* 186 */
/***/ (function(module, exports, __webpack_require__) {

exports.f = __webpack_require__(21);


/***/ }),
/* 187 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(17);
var core = __webpack_require__(7);
var LIBRARY = __webpack_require__(104);
var wksExt = __webpack_require__(186);
var defineProperty = __webpack_require__(26).f;
module.exports = function (name) {
  var $Symbol = core.Symbol || (core.Symbol = LIBRARY ? {} : global.Symbol || {});
  if (name.charAt(0) != '_' && !(name in $Symbol)) defineProperty($Symbol, name, { value: wksExt.f(name) });
};


/***/ }),
/* 188 */
/***/ (function(module, exports) {

exports.f = Object.getOwnPropertySymbols;


/***/ }),
/* 189 */
/***/ (function(module, exports, __webpack_require__) {

var hide = __webpack_require__(41);
module.exports = function (target, src, safe) {
  for (var key in src) {
    if (safe && target[key]) target[key] = src[key];
    else hide(target, key, src[key]);
  } return target;
};


/***/ }),
/* 190 */
/***/ (function(module, exports) {

module.exports = function (it, Constructor, name, forbiddenField) {
  if (!(it instanceof Constructor) || (forbiddenField !== undefined && forbiddenField in it)) {
    throw TypeError(name + ': incorrect invocation!');
  } return it;
};


/***/ }),
/* 191 */
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__(22);
module.exports = function (it, TYPE) {
  if (!isObject(it) || it._t !== TYPE) throw TypeError('Incompatible receiver, ' + TYPE + ' required!');
  return it;
};


/***/ }),
/* 192 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var $indexOf = __webpack_require__(93).indexOf;
var arrayMethodIsStrict = __webpack_require__(59);
var arrayMethodUsesToLength = __webpack_require__(31);

var nativeIndexOf = [].indexOf;

var NEGATIVE_ZERO = !!nativeIndexOf && 1 / [1].indexOf(1, -0) < 0;
var STRICT_METHOD = arrayMethodIsStrict('indexOf');
var USES_TO_LENGTH = arrayMethodUsesToLength('indexOf', { ACCESSORS: true, 1: 0 });

// `Array.prototype.indexOf` method
// https://tc39.github.io/ecma262/#sec-array.prototype.indexof
$({ target: 'Array', proto: true, forced: NEGATIVE_ZERO || !STRICT_METHOD || !USES_TO_LENGTH }, {
  indexOf: function indexOf(searchElement /* , fromIndex = 0 */) {
    return NEGATIVE_ZERO
      // convert -0 to +0
      ? nativeIndexOf.apply(this, arguments) || 0
      : $indexOf(this, searchElement, arguments.length > 1 ? arguments[1] : undefined);
  }
});


/***/ }),
/* 193 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

// 25.4.1.5 NewPromiseCapability(C)
var aFunction = __webpack_require__(101);

function PromiseCapability(C) {
  var resolve, reject;
  this.promise = new C(function ($$resolve, $$reject) {
    if (resolve !== undefined || reject !== undefined) throw TypeError('Bad Promise constructor');
    resolve = $$resolve;
    reject = $$reject;
  });
  this.resolve = aFunction(resolve);
  this.reject = aFunction(reject);
}

module.exports.f = function (C) {
  return new PromiseCapability(C);
};


/***/ }),
/* 194 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var isString = __webpack_require__(0).isString;
var isNumber = __webpack_require__(0).isNumber;
var isObject = __webpack_require__(0).isObject;
var isArray = __webpack_require__(0).isArray;
var isUndefined = __webpack_require__(0).isUndefined;
var LineBreaker = __webpack_require__(290);

var LEADING = /^(\s)+/g;
var TRAILING = /(\s)+$/g;

/**
 * Creates an instance of TextTools - text measurement utility
 *
 * @constructor
 * @param {FontProvider} fontProvider
 */
function TextTools(fontProvider) {
	this.fontProvider = fontProvider;
}

/**
 * Converts an array of strings (or inline-definition-objects) into a collection
 * of inlines and calculated minWidth/maxWidth.
 * and their min/max widths
 * @param  {Object} textArray - an array of inline-definition-objects (or strings)
 * @param  {Object} styleContextStack current style stack
 * @return {Object}                   collection of inlines, minWidth, maxWidth
 */
TextTools.prototype.buildInlines = function (textArray, styleContextStack) {
	var measured = measure(this.fontProvider, textArray, styleContextStack);

	var minWidth = 0,
		maxWidth = 0,
		currentLineWidth;

	measured.forEach(function (inline) {
		minWidth = Math.max(minWidth, inline.width - inline.leadingCut - inline.trailingCut);

		if (!currentLineWidth) {
			currentLineWidth = { width: 0, leadingCut: inline.leadingCut, trailingCut: 0 };
		}

		currentLineWidth.width += inline.width;
		currentLineWidth.trailingCut = inline.trailingCut;

		maxWidth = Math.max(maxWidth, getTrimmedWidth(currentLineWidth));

		if (inline.lineEnd) {
			currentLineWidth = null;
		}
	});

	if (getStyleProperty({}, styleContextStack, 'noWrap', false)) {
		minWidth = maxWidth;
	}

	return {
		items: measured,
		minWidth: minWidth,
		maxWidth: maxWidth
	};

	function getTrimmedWidth(item) {
		return Math.max(0, item.width - item.leadingCut - item.trailingCut);
	}
};

/**
 * Returns size of the specified string (without breaking it) using the current style
 * @param  {String} text              text to be measured
 * @param  {Object} styleContextStack current style stack
 * @return {Object}                   size of the specified string
 */
TextTools.prototype.sizeOfString = function (text, styleContextStack) {
	text = text ? text.toString().replace(/\t/g, '    ') : '';

	//TODO: refactor - extract from measure
	var fontName = getStyleProperty({}, styleContextStack, 'font', 'Roboto');
	var fontSize = getStyleProperty({}, styleContextStack, 'fontSize', 12);
	var fontFeatures = getStyleProperty({}, styleContextStack, 'fontFeatures', null);
	var bold = getStyleProperty({}, styleContextStack, 'bold', false);
	var italics = getStyleProperty({}, styleContextStack, 'italics', false);
	var lineHeight = getStyleProperty({}, styleContextStack, 'lineHeight', 1);
	var characterSpacing = getStyleProperty({}, styleContextStack, 'characterSpacing', 0);

	var font = this.fontProvider.provideFont(fontName, bold, italics);

	return {
		width: widthOfString(text, font, fontSize, characterSpacing, fontFeatures),
		height: font.lineHeight(fontSize) * lineHeight,
		fontSize: fontSize,
		lineHeight: lineHeight,
		ascender: font.ascender / 1000 * fontSize,
		descender: font.descender / 1000 * fontSize
	};
};

/**
 * Returns size of the specified rotated string (without breaking it) using the current style
 *
 * @param  {string} text text to be measured
 * @param  {number} angle
 * @param  {object} styleContextStack current style stack
 * @returns {object} size of the specified string
 */
TextTools.prototype.sizeOfRotatedText = function (text, angle, styleContextStack) {
	var angleRad = angle * Math.PI / -180;
	var size = this.sizeOfString(text, styleContextStack);
	return {
		width: Math.abs(size.height * Math.sin(angleRad)) + Math.abs(size.width * Math.cos(angleRad)),
		height: Math.abs(size.width * Math.sin(angleRad)) + Math.abs(size.height * Math.cos(angleRad))
	};
}

TextTools.prototype.widthOfString = function (text, font, fontSize, characterSpacing, fontFeatures) {
	return widthOfString(text, font, fontSize, characterSpacing, fontFeatures);
};

function splitWords(text, noWrap) {
	var results = [];
	text = text.replace(/\t/g, '    ');

	if (noWrap) {
		results.push({ text: text });
		return results;
	}

	var breaker = new LineBreaker(text);
	var last = 0;
	var bk;

	while (bk = breaker.nextBreak()) {
		var word = text.slice(last, bk.position);

		if (bk.required || word.match(/\r?\n$|\r$/)) { // new line
			word = word.replace(/\r?\n$|\r$/, '');
			results.push({ text: word, lineEnd: true });
		} else {
			results.push({ text: word });
		}

		last = bk.position;
	}

	return results;
}

function copyStyle(source, destination) {
	destination = destination || {};
	source = source || {}; //TODO: default style

	for (var key in source) {
		if (key != 'text' && source.hasOwnProperty(key)) {
			destination[key] = source[key];
		}
	}

	return destination;
}

function normalizeTextArray(array, styleContextStack) {
	function flatten(array) {
		return array.reduce(function (prev, cur) {
			var current = isArray(cur.text) ? flatten(cur.text) : cur;
			var more = [].concat(current).some(Array.isArray);
			return prev.concat(more ? flatten(current) : current);
		}, []);
	}

	function getOneWord(index, words, noWrap) {
		if (isUndefined(words[index])) {
			return null;
		}

		if (words[index].lineEnd) {
			return null;
		}

		var word = words[index].text;

		if (noWrap) {
			var tmpWords = splitWords(normalizeString(word), false);
			if (isUndefined(tmpWords[tmpWords.length - 1])) {
				return null;
			}
			word = tmpWords[tmpWords.length - 1].text;
		}

		return word;
	}

	var results = [];

	if (!isArray(array)) {
		array = [array];
	}

	array = flatten(array);

	var lastWord = null;
	for (var i = 0, l = array.length; i < l; i++) {
		var item = array[i];
		var style = null;
		var words;

		var noWrap = getStyleProperty(item || {}, styleContextStack, 'noWrap', false);
		if (isObject(item)) {
			if (item._textRef && item._textRef._textNodeRef.text) {
				item.text = item._textRef._textNodeRef.text;
			}
			words = splitWords(normalizeString(item.text), noWrap);
			style = copyStyle(item);
		} else {
			words = splitWords(normalizeString(item), noWrap);
		}

		if (lastWord && words.length) {
			var firstWord = getOneWord(0, words, noWrap);

			var wrapWords = splitWords(normalizeString(lastWord + firstWord), false);
			if (wrapWords.length === 1) {
				results[results.length - 1].noNewLine = true;
			}
		}

		for (var i2 = 0, l2 = words.length; i2 < l2; i2++) {
			var result = {
				text: words[i2].text
			};

			if (words[i2].lineEnd) {
				result.lineEnd = true;
			}

			copyStyle(style, result);

			results.push(result);
		}

		lastWord = null;
		if (i + 1 < l) {
			lastWord = getOneWord(words.length - 1, words, noWrap);
		}
	}

	return results;
}

function normalizeString(value) {
	if (value === undefined || value === null) {
		return '';
	} else if (isNumber(value)) {
		return value.toString();
	} else if (isString(value)) {
		return value;
	} else {
		return value.toString();
	}
}

function getStyleProperty(item, styleContextStack, property, defaultValue) {
	var value;

	if (item[property] !== undefined && item[property] !== null) {
		// item defines this property
		return item[property];
	}

	if (!styleContextStack) {
		return defaultValue;
	}

	styleContextStack.auto(item, function () {
		value = styleContextStack.getProperty(property);
	});

	if (value !== null && value !== undefined) {
		return value;
	} else {
		return defaultValue;
	}
}

function measure(fontProvider, textArray, styleContextStack) {
	var normalized = normalizeTextArray(textArray, styleContextStack);

	if (normalized.length) {
		var leadingIndent = getStyleProperty(normalized[0], styleContextStack, 'leadingIndent', 0);

		if (leadingIndent) {
			normalized[0].leadingCut = -leadingIndent;
			normalized[0].leadingIndent = leadingIndent;
		}
	}

	normalized.forEach(function (item) {
		var fontName = getStyleProperty(item, styleContextStack, 'font', 'Roboto');
		var fontSize = getStyleProperty(item, styleContextStack, 'fontSize', 12);
		var fontFeatures = getStyleProperty(item, styleContextStack, 'fontFeatures', null);
		var bold = getStyleProperty(item, styleContextStack, 'bold', false);
		var italics = getStyleProperty(item, styleContextStack, 'italics', false);
		var color = getStyleProperty(item, styleContextStack, 'color', 'black');
		var decoration = getStyleProperty(item, styleContextStack, 'decoration', null);
		var decorationColor = getStyleProperty(item, styleContextStack, 'decorationColor', null);
		var decorationStyle = getStyleProperty(item, styleContextStack, 'decorationStyle', null);
		var background = getStyleProperty(item, styleContextStack, 'background', null);
		var lineHeight = getStyleProperty(item, styleContextStack, 'lineHeight', 1);
		var characterSpacing = getStyleProperty(item, styleContextStack, 'characterSpacing', 0);
		var link = getStyleProperty(item, styleContextStack, 'link', null);
		var linkToPage = getStyleProperty(item, styleContextStack, 'linkToPage', null);
		var linkToDestination = getStyleProperty(item, styleContextStack, 'linkToDestination', null);
		var noWrap = getStyleProperty(item, styleContextStack, 'noWrap', null);
		var preserveLeadingSpaces = getStyleProperty(item, styleContextStack, 'preserveLeadingSpaces', false);
		var preserveTrailingSpaces = getStyleProperty(item, styleContextStack, 'preserveTrailingSpaces', false);
		var opacity = getStyleProperty(item, styleContextStack, 'opacity', 1);

		var font = fontProvider.provideFont(fontName, bold, italics);

		item.width = widthOfString(item.text, font, fontSize, characterSpacing, fontFeatures);
		item.height = font.lineHeight(fontSize) * lineHeight;

		if (!item.leadingCut) {
			item.leadingCut = 0;
		}

		var leadingSpaces;
		if (!preserveLeadingSpaces && (leadingSpaces = item.text.match(LEADING))) {
			item.leadingCut += widthOfString(leadingSpaces[0], font, fontSize, characterSpacing, fontFeatures);
		}

		var trailingSpaces;
		if (!preserveTrailingSpaces && (trailingSpaces = item.text.match(TRAILING))) {
			item.trailingCut = widthOfString(trailingSpaces[0], font, fontSize, characterSpacing, fontFeatures);
		} else {
			item.trailingCut = 0;
		}

		item.alignment = getStyleProperty(item, styleContextStack, 'alignment', 'left');
		item.font = font;
		item.fontSize = fontSize;
		item.fontFeatures = fontFeatures;
		item.characterSpacing = characterSpacing;
		item.color = color;
		item.decoration = decoration;
		item.decorationColor = decorationColor;
		item.decorationStyle = decorationStyle;
		item.background = background;
		item.link = link;
		item.linkToPage = linkToPage;
		item.linkToDestination = linkToDestination;
		item.noWrap = noWrap;
		item.opacity = opacity;
	});

	return normalized;
}

function widthOfString(text, font, fontSize, characterSpacing, fontFeatures) {
	return font.widthOfString(text, fontSize, fontFeatures) + ((characterSpacing || 0) * (text.length - 1));
}

module.exports = TextTools;


/***/ }),
/* 195 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var isString = __webpack_require__(0).isString;

function buildColumnWidths(columns, availableWidth) {
	var autoColumns = [],
		autoMin = 0, autoMax = 0,
		starColumns = [],
		starMaxMin = 0,
		starMaxMax = 0,
		fixedColumns = [],
		initial_availableWidth = availableWidth;

	columns.forEach(function (column) {
		if (isAutoColumn(column)) {
			autoColumns.push(column);
			autoMin += column._minWidth;
			autoMax += column._maxWidth;
		} else if (isStarColumn(column)) {
			starColumns.push(column);
			starMaxMin = Math.max(starMaxMin, column._minWidth);
			starMaxMax = Math.max(starMaxMax, column._maxWidth);
		} else {
			fixedColumns.push(column);
		}
	});

	fixedColumns.forEach(function (col) {
		// width specified as %
		if (isString(col.width) && /\d+%/.test(col.width)) {
			col.width = parseFloat(col.width) * initial_availableWidth / 100;
		}
		if (col.width < (col._minWidth) && col.elasticWidth) {
			col._calcWidth = col._minWidth;
		} else {
			col._calcWidth = col.width;
		}

		availableWidth -= col._calcWidth;
	});

	// http://www.freesoft.org/CIE/RFC/1942/18.htm
	// http://www.w3.org/TR/CSS2/tables.html#width-layout
	// http://dev.w3.org/csswg/css3-tables-algorithms/Overview.src.htm
	var minW = autoMin + starMaxMin * starColumns.length;
	var maxW = autoMax + starMaxMax * starColumns.length;
	if (minW >= availableWidth) {
		// case 1 - there's no way to fit all columns within available width
		// that's actually pretty bad situation with PDF as we have no horizontal scroll
		// no easy workaround (unless we decide, in the future, to split single words)
		// currently we simply use minWidths for all columns
		autoColumns.forEach(function (col) {
			col._calcWidth = col._minWidth;
		});

		starColumns.forEach(function (col) {
			col._calcWidth = starMaxMin; // starMaxMin already contains padding
		});
	} else {
		if (maxW < availableWidth) {
			// case 2 - we can fit rest of the table within available space
			autoColumns.forEach(function (col) {
				col._calcWidth = col._maxWidth;
				availableWidth -= col._calcWidth;
			});
		} else {
			// maxW is too large, but minW fits within available width
			var W = availableWidth - minW;
			var D = maxW - minW;

			autoColumns.forEach(function (col) {
				var d = col._maxWidth - col._minWidth;
				col._calcWidth = col._minWidth + d * W / D;
				availableWidth -= col._calcWidth;
			});
		}

		if (starColumns.length > 0) {
			var starSize = availableWidth / starColumns.length;

			starColumns.forEach(function (col) {
				col._calcWidth = starSize;
			});
		}
	}
}

function isAutoColumn(column) {
	return column.width === 'auto';
}

function isStarColumn(column) {
	return column.width === null || column.width === undefined || column.width === '*' || column.width === 'star';
}

//TODO: refactor and reuse in measureTable
function measureMinMax(columns) {
	var result = { min: 0, max: 0 };

	var maxStar = { min: 0, max: 0 };
	var starCount = 0;

	for (var i = 0, l = columns.length; i < l; i++) {
		var c = columns[i];

		if (isStarColumn(c)) {
			maxStar.min = Math.max(maxStar.min, c._minWidth);
			maxStar.max = Math.max(maxStar.max, c._maxWidth);
			starCount++;
		} else if (isAutoColumn(c)) {
			result.min += c._minWidth;
			result.max += c._maxWidth;
		} else {
			result.min += ((c.width !== undefined && c.width) || c._minWidth);
			result.max += ((c.width !== undefined && c.width) || c._maxWidth);
		}
	}

	if (starCount) {
		result.min += starCount * maxStar.min;
		result.max += starCount * maxStar.max;
	}

	return result;
}

/**
 * Calculates column widths
 * @private
 */
module.exports = {
	buildColumnWidths: buildColumnWidths,
	measureMinMax: measureMinMax,
	isAutoColumn: isAutoColumn,
	isStarColumn: isStarColumn
};


/***/ }),
/* 196 */
/***/ (function(module, exports) {

var toString = {}.toString;

module.exports = Array.isArray || function (arr) {
  return toString.call(arr) == '[object Array]';
};


/***/ }),
/* 197 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var global = __webpack_require__(3);
var getBuiltIn = __webpack_require__(36);
var IS_PURE = __webpack_require__(56);
var DESCRIPTORS = __webpack_require__(11);
var NATIVE_SYMBOL = __webpack_require__(145);
var USE_SYMBOL_AS_UID = __webpack_require__(202);
var fails = __webpack_require__(4);
var has = __webpack_require__(14);
var isArray = __webpack_require__(115);
var isObject = __webpack_require__(13);
var anObject = __webpack_require__(16);
var toObject = __webpack_require__(19);
var toIndexedObject = __webpack_require__(27);
var toPrimitive = __webpack_require__(55);
var createPropertyDescriptor = __webpack_require__(42);
var nativeObjectCreate = __webpack_require__(58);
var objectKeys = __webpack_require__(94);
var getOwnPropertyNamesModule = __webpack_require__(57);
var getOwnPropertyNamesExternal = __webpack_require__(311);
var getOwnPropertySymbolsModule = __webpack_require__(143);
var getOwnPropertyDescriptorModule = __webpack_require__(54);
var definePropertyModule = __webpack_require__(15);
var propertyIsEnumerableModule = __webpack_require__(110);
var createNonEnumerableProperty = __webpack_require__(18);
var redefine = __webpack_require__(25);
var shared = __webpack_require__(141);
var sharedKey = __webpack_require__(111);
var hiddenKeys = __webpack_require__(113);
var uid = __webpack_require__(112);
var wellKnownSymbol = __webpack_require__(6);
var wrappedWellKnownSymbolModule = __webpack_require__(204);
var defineWellKnownSymbol = __webpack_require__(205);
var setToStringTag = __webpack_require__(95);
var InternalStateModule = __webpack_require__(35);
var $forEach = __webpack_require__(20).forEach;

var HIDDEN = sharedKey('hidden');
var SYMBOL = 'Symbol';
var PROTOTYPE = 'prototype';
var TO_PRIMITIVE = wellKnownSymbol('toPrimitive');
var setInternalState = InternalStateModule.set;
var getInternalState = InternalStateModule.getterFor(SYMBOL);
var ObjectPrototype = Object[PROTOTYPE];
var $Symbol = global.Symbol;
var $stringify = getBuiltIn('JSON', 'stringify');
var nativeGetOwnPropertyDescriptor = getOwnPropertyDescriptorModule.f;
var nativeDefineProperty = definePropertyModule.f;
var nativeGetOwnPropertyNames = getOwnPropertyNamesExternal.f;
var nativePropertyIsEnumerable = propertyIsEnumerableModule.f;
var AllSymbols = shared('symbols');
var ObjectPrototypeSymbols = shared('op-symbols');
var StringToSymbolRegistry = shared('string-to-symbol-registry');
var SymbolToStringRegistry = shared('symbol-to-string-registry');
var WellKnownSymbolsStore = shared('wks');
var QObject = global.QObject;
// Don't use setters in Qt Script, https://github.com/zloirock/core-js/issues/173
var USE_SETTER = !QObject || !QObject[PROTOTYPE] || !QObject[PROTOTYPE].findChild;

// fallback for old Android, https://code.google.com/p/v8/issues/detail?id=687
var setSymbolDescriptor = DESCRIPTORS && fails(function () {
  return nativeObjectCreate(nativeDefineProperty({}, 'a', {
    get: function () { return nativeDefineProperty(this, 'a', { value: 7 }).a; }
  })).a != 7;
}) ? function (O, P, Attributes) {
  var ObjectPrototypeDescriptor = nativeGetOwnPropertyDescriptor(ObjectPrototype, P);
  if (ObjectPrototypeDescriptor) delete ObjectPrototype[P];
  nativeDefineProperty(O, P, Attributes);
  if (ObjectPrototypeDescriptor && O !== ObjectPrototype) {
    nativeDefineProperty(ObjectPrototype, P, ObjectPrototypeDescriptor);
  }
} : nativeDefineProperty;

var wrap = function (tag, description) {
  var symbol = AllSymbols[tag] = nativeObjectCreate($Symbol[PROTOTYPE]);
  setInternalState(symbol, {
    type: SYMBOL,
    tag: tag,
    description: description
  });
  if (!DESCRIPTORS) symbol.description = description;
  return symbol;
};

var isSymbol = USE_SYMBOL_AS_UID ? function (it) {
  return typeof it == 'symbol';
} : function (it) {
  return Object(it) instanceof $Symbol;
};

var $defineProperty = function defineProperty(O, P, Attributes) {
  if (O === ObjectPrototype) $defineProperty(ObjectPrototypeSymbols, P, Attributes);
  anObject(O);
  var key = toPrimitive(P, true);
  anObject(Attributes);
  if (has(AllSymbols, key)) {
    if (!Attributes.enumerable) {
      if (!has(O, HIDDEN)) nativeDefineProperty(O, HIDDEN, createPropertyDescriptor(1, {}));
      O[HIDDEN][key] = true;
    } else {
      if (has(O, HIDDEN) && O[HIDDEN][key]) O[HIDDEN][key] = false;
      Attributes = nativeObjectCreate(Attributes, { enumerable: createPropertyDescriptor(0, false) });
    } return setSymbolDescriptor(O, key, Attributes);
  } return nativeDefineProperty(O, key, Attributes);
};

var $defineProperties = function defineProperties(O, Properties) {
  anObject(O);
  var properties = toIndexedObject(Properties);
  var keys = objectKeys(properties).concat($getOwnPropertySymbols(properties));
  $forEach(keys, function (key) {
    if (!DESCRIPTORS || $propertyIsEnumerable.call(properties, key)) $defineProperty(O, key, properties[key]);
  });
  return O;
};

var $create = function create(O, Properties) {
  return Properties === undefined ? nativeObjectCreate(O) : $defineProperties(nativeObjectCreate(O), Properties);
};

var $propertyIsEnumerable = function propertyIsEnumerable(V) {
  var P = toPrimitive(V, true);
  var enumerable = nativePropertyIsEnumerable.call(this, P);
  if (this === ObjectPrototype && has(AllSymbols, P) && !has(ObjectPrototypeSymbols, P)) return false;
  return enumerable || !has(this, P) || !has(AllSymbols, P) || has(this, HIDDEN) && this[HIDDEN][P] ? enumerable : true;
};

var $getOwnPropertyDescriptor = function getOwnPropertyDescriptor(O, P) {
  var it = toIndexedObject(O);
  var key = toPrimitive(P, true);
  if (it === ObjectPrototype && has(AllSymbols, key) && !has(ObjectPrototypeSymbols, key)) return;
  var descriptor = nativeGetOwnPropertyDescriptor(it, key);
  if (descriptor && has(AllSymbols, key) && !(has(it, HIDDEN) && it[HIDDEN][key])) {
    descriptor.enumerable = true;
  }
  return descriptor;
};

var $getOwnPropertyNames = function getOwnPropertyNames(O) {
  var names = nativeGetOwnPropertyNames(toIndexedObject(O));
  var result = [];
  $forEach(names, function (key) {
    if (!has(AllSymbols, key) && !has(hiddenKeys, key)) result.push(key);
  });
  return result;
};

var $getOwnPropertySymbols = function getOwnPropertySymbols(O) {
  var IS_OBJECT_PROTOTYPE = O === ObjectPrototype;
  var names = nativeGetOwnPropertyNames(IS_OBJECT_PROTOTYPE ? ObjectPrototypeSymbols : toIndexedObject(O));
  var result = [];
  $forEach(names, function (key) {
    if (has(AllSymbols, key) && (!IS_OBJECT_PROTOTYPE || has(ObjectPrototype, key))) {
      result.push(AllSymbols[key]);
    }
  });
  return result;
};

// `Symbol` constructor
// https://tc39.github.io/ecma262/#sec-symbol-constructor
if (!NATIVE_SYMBOL) {
  $Symbol = function Symbol() {
    if (this instanceof $Symbol) throw TypeError('Symbol is not a constructor');
    var description = !arguments.length || arguments[0] === undefined ? undefined : String(arguments[0]);
    var tag = uid(description);
    var setter = function (value) {
      if (this === ObjectPrototype) setter.call(ObjectPrototypeSymbols, value);
      if (has(this, HIDDEN) && has(this[HIDDEN], tag)) this[HIDDEN][tag] = false;
      setSymbolDescriptor(this, tag, createPropertyDescriptor(1, value));
    };
    if (DESCRIPTORS && USE_SETTER) setSymbolDescriptor(ObjectPrototype, tag, { configurable: true, set: setter });
    return wrap(tag, description);
  };

  redefine($Symbol[PROTOTYPE], 'toString', function toString() {
    return getInternalState(this).tag;
  });

  redefine($Symbol, 'withoutSetter', function (description) {
    return wrap(uid(description), description);
  });

  propertyIsEnumerableModule.f = $propertyIsEnumerable;
  definePropertyModule.f = $defineProperty;
  getOwnPropertyDescriptorModule.f = $getOwnPropertyDescriptor;
  getOwnPropertyNamesModule.f = getOwnPropertyNamesExternal.f = $getOwnPropertyNames;
  getOwnPropertySymbolsModule.f = $getOwnPropertySymbols;

  wrappedWellKnownSymbolModule.f = function (name) {
    return wrap(wellKnownSymbol(name), name);
  };

  if (DESCRIPTORS) {
    // https://github.com/tc39/proposal-Symbol-description
    nativeDefineProperty($Symbol[PROTOTYPE], 'description', {
      configurable: true,
      get: function description() {
        return getInternalState(this).description;
      }
    });
    if (!IS_PURE) {
      redefine(ObjectPrototype, 'propertyIsEnumerable', $propertyIsEnumerable, { unsafe: true });
    }
  }
}

$({ global: true, wrap: true, forced: !NATIVE_SYMBOL, sham: !NATIVE_SYMBOL }, {
  Symbol: $Symbol
});

$forEach(objectKeys(WellKnownSymbolsStore), function (name) {
  defineWellKnownSymbol(name);
});

$({ target: SYMBOL, stat: true, forced: !NATIVE_SYMBOL }, {
  // `Symbol.for` method
  // https://tc39.github.io/ecma262/#sec-symbol.for
  'for': function (key) {
    var string = String(key);
    if (has(StringToSymbolRegistry, string)) return StringToSymbolRegistry[string];
    var symbol = $Symbol(string);
    StringToSymbolRegistry[string] = symbol;
    SymbolToStringRegistry[symbol] = string;
    return symbol;
  },
  // `Symbol.keyFor` method
  // https://tc39.github.io/ecma262/#sec-symbol.keyfor
  keyFor: function keyFor(sym) {
    if (!isSymbol(sym)) throw TypeError(sym + ' is not a symbol');
    if (has(SymbolToStringRegistry, sym)) return SymbolToStringRegistry[sym];
  },
  useSetter: function () { USE_SETTER = true; },
  useSimple: function () { USE_SETTER = false; }
});

$({ target: 'Object', stat: true, forced: !NATIVE_SYMBOL, sham: !DESCRIPTORS }, {
  // `Object.create` method
  // https://tc39.github.io/ecma262/#sec-object.create
  create: $create,
  // `Object.defineProperty` method
  // https://tc39.github.io/ecma262/#sec-object.defineproperty
  defineProperty: $defineProperty,
  // `Object.defineProperties` method
  // https://tc39.github.io/ecma262/#sec-object.defineproperties
  defineProperties: $defineProperties,
  // `Object.getOwnPropertyDescriptor` method
  // https://tc39.github.io/ecma262/#sec-object.getownpropertydescriptors
  getOwnPropertyDescriptor: $getOwnPropertyDescriptor
});

$({ target: 'Object', stat: true, forced: !NATIVE_SYMBOL }, {
  // `Object.getOwnPropertyNames` method
  // https://tc39.github.io/ecma262/#sec-object.getownpropertynames
  getOwnPropertyNames: $getOwnPropertyNames,
  // `Object.getOwnPropertySymbols` method
  // https://tc39.github.io/ecma262/#sec-object.getownpropertysymbols
  getOwnPropertySymbols: $getOwnPropertySymbols
});

// Chrome 38 and 39 `Object.getOwnPropertySymbols` fails on primitives
// https://bugs.chromium.org/p/v8/issues/detail?id=3443
$({ target: 'Object', stat: true, forced: fails(function () { getOwnPropertySymbolsModule.f(1); }) }, {
  getOwnPropertySymbols: function getOwnPropertySymbols(it) {
    return getOwnPropertySymbolsModule.f(toObject(it));
  }
});

// `JSON.stringify` method behavior with symbols
// https://tc39.github.io/ecma262/#sec-json.stringify
if ($stringify) {
  var FORCED_JSON_STRINGIFY = !NATIVE_SYMBOL || fails(function () {
    var symbol = $Symbol();
    // MS Edge converts symbol values to JSON as {}
    return $stringify([symbol]) != '[null]'
      // WebKit converts symbol values to JSON as null
      || $stringify({ a: symbol }) != '{}'
      // V8 throws on boxed symbols
      || $stringify(Object(symbol)) != '{}';
  });

  $({ target: 'JSON', stat: true, forced: FORCED_JSON_STRINGIFY }, {
    // eslint-disable-next-line no-unused-vars
    stringify: function stringify(it, replacer, space) {
      var args = [it];
      var index = 1;
      var $replacer;
      while (arguments.length > index) args.push(arguments[index++]);
      $replacer = replacer;
      if (!isObject(replacer) && it === undefined || isSymbol(it)) return; // IE8 returns string on undefined
      if (!isArray(replacer)) replacer = function (key, value) {
        if (typeof $replacer == 'function') value = $replacer.call(this, key, value);
        if (!isSymbol(value)) return value;
      };
      args[1] = replacer;
      return $stringify.apply(null, args);
    }
  });
}

// `Symbol.prototype[@@toPrimitive]` method
// https://tc39.github.io/ecma262/#sec-symbol.prototype-@@toprimitive
if (!$Symbol[PROTOTYPE][TO_PRIMITIVE]) {
  createNonEnumerableProperty($Symbol[PROTOTYPE], TO_PRIMITIVE, $Symbol[PROTOTYPE].valueOf);
}
// `Symbol.prototype[@@toStringTag]` property
// https://tc39.github.io/ecma262/#sec-symbol.prototype-@@tostringtag
setToStringTag($Symbol, SYMBOL);

hiddenKeys[HIDDEN] = true;


/***/ }),
/* 198 */
/***/ (function(module, exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(11);
var fails = __webpack_require__(4);
var createElement = __webpack_require__(138);

// Thank's IE8 for his funny defineProperty
module.exports = !DESCRIPTORS && !fails(function () {
  return Object.defineProperty(createElement('div'), 'a', {
    get: function () { return 7; }
  }).a != 7;
});


/***/ }),
/* 199 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var setGlobal = __webpack_require__(139);

var SHARED = '__core-js_shared__';
var store = global[SHARED] || setGlobal(SHARED, {});

module.exports = store;


/***/ }),
/* 200 */
/***/ (function(module, exports, __webpack_require__) {

var has = __webpack_require__(14);
var ownKeys = __webpack_require__(309);
var getOwnPropertyDescriptorModule = __webpack_require__(54);
var definePropertyModule = __webpack_require__(15);

module.exports = function (target, source) {
  var keys = ownKeys(source);
  var defineProperty = definePropertyModule.f;
  var getOwnPropertyDescriptor = getOwnPropertyDescriptorModule.f;
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (!has(target, key)) defineProperty(target, key, getOwnPropertyDescriptor(source, key));
  }
};


/***/ }),
/* 201 */
/***/ (function(module, exports, __webpack_require__) {

var has = __webpack_require__(14);
var toIndexedObject = __webpack_require__(27);
var indexOf = __webpack_require__(93).indexOf;
var hiddenKeys = __webpack_require__(113);

module.exports = function (object, names) {
  var O = toIndexedObject(object);
  var i = 0;
  var result = [];
  var key;
  for (key in O) !has(hiddenKeys, key) && has(O, key) && result.push(key);
  // Don't enum bug & hidden keys
  while (names.length > i) if (has(O, key = names[i++])) {
    ~indexOf(result, key) || result.push(key);
  }
  return result;
};


/***/ }),
/* 202 */
/***/ (function(module, exports, __webpack_require__) {

var NATIVE_SYMBOL = __webpack_require__(145);

module.exports = NATIVE_SYMBOL
  // eslint-disable-next-line no-undef
  && !Symbol.sham
  // eslint-disable-next-line no-undef
  && typeof Symbol.iterator == 'symbol';


/***/ }),
/* 203 */
/***/ (function(module, exports, __webpack_require__) {

var getBuiltIn = __webpack_require__(36);

module.exports = getBuiltIn('document', 'documentElement');


/***/ }),
/* 204 */
/***/ (function(module, exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(6);

exports.f = wellKnownSymbol;


/***/ }),
/* 205 */
/***/ (function(module, exports, __webpack_require__) {

var path = __webpack_require__(114);
var has = __webpack_require__(14);
var wrappedWellKnownSymbolModule = __webpack_require__(204);
var defineProperty = __webpack_require__(15).f;

module.exports = function (NAME) {
  var Symbol = path.Symbol || (path.Symbol = {});
  if (!has(Symbol, NAME)) defineProperty(Symbol, NAME, {
    value: wrappedWellKnownSymbolModule.f(NAME)
  });
};


/***/ }),
/* 206 */
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__(13);
var isArray = __webpack_require__(115);
var wellKnownSymbol = __webpack_require__(6);

var SPECIES = wellKnownSymbol('species');

// `ArraySpeciesCreate` abstract operation
// https://tc39.github.io/ecma262/#sec-arrayspeciescreate
module.exports = function (originalArray, length) {
  var C;
  if (isArray(originalArray)) {
    C = originalArray.constructor;
    // cross-realm fallback
    if (typeof C == 'function' && (C === Array || isArray(C.prototype))) C = undefined;
    else if (isObject(C)) {
      C = C[SPECIES];
      if (C === null) C = undefined;
    }
  } return new (C === undefined ? Array : C)(length === 0 ? 0 : length);
};


/***/ }),
/* 207 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
// `Symbol.prototype.description` getter
// https://tc39.github.io/ecma262/#sec-symbol.prototype.description

var $ = __webpack_require__(1);
var DESCRIPTORS = __webpack_require__(11);
var global = __webpack_require__(3);
var has = __webpack_require__(14);
var isObject = __webpack_require__(13);
var defineProperty = __webpack_require__(15).f;
var copyConstructorProperties = __webpack_require__(200);

var NativeSymbol = global.Symbol;

if (DESCRIPTORS && typeof NativeSymbol == 'function' && (!('description' in NativeSymbol.prototype) ||
  // Safari 12 bug
  NativeSymbol().description !== undefined
)) {
  var EmptyStringDescriptionStore = {};
  // wrap Symbol constructor for correct work with undefined description
  var SymbolWrapper = function Symbol() {
    var description = arguments.length < 1 || arguments[0] === undefined ? undefined : String(arguments[0]);
    var result = this instanceof SymbolWrapper
      ? new NativeSymbol(description)
      // in Edge 13, String(Symbol(undefined)) === 'Symbol(undefined)'
      : description === undefined ? NativeSymbol() : NativeSymbol(description);
    if (description === '') EmptyStringDescriptionStore[result] = true;
    return result;
  };
  copyConstructorProperties(SymbolWrapper, NativeSymbol);
  var symbolPrototype = SymbolWrapper.prototype = NativeSymbol.prototype;
  symbolPrototype.constructor = SymbolWrapper;

  var symbolToString = symbolPrototype.toString;
  var native = String(NativeSymbol('test')) == 'Symbol(test)';
  var regexp = /^Symbol\((.*)\)[^)]+$/;
  defineProperty(symbolPrototype, 'description', {
    configurable: true,
    get: function description() {
      var symbol = isObject(this) ? this.valueOf() : this;
      var string = symbolToString.call(symbol);
      if (has(EmptyStringDescriptionStore, symbol)) return '';
      var desc = native ? string.slice(7, -1) : string.replace(regexp, '$1');
      return desc === '' ? undefined : desc;
    }
  });

  $({ global: true, forced: true }, {
    Symbol: SymbolWrapper
  });
}


/***/ }),
/* 208 */
/***/ (function(module, exports, __webpack_require__) {

var defineWellKnownSymbol = __webpack_require__(205);

// `Symbol.iterator` well-known symbol
// https://tc39.github.io/ecma262/#sec-symbol.iterator
defineWellKnownSymbol('iterator');


/***/ }),
/* 209 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var fails = __webpack_require__(4);
var isArray = __webpack_require__(115);
var isObject = __webpack_require__(13);
var toObject = __webpack_require__(19);
var toLength = __webpack_require__(8);
var createProperty = __webpack_require__(146);
var arraySpeciesCreate = __webpack_require__(206);
var arrayMethodHasSpeciesSupport = __webpack_require__(116);
var wellKnownSymbol = __webpack_require__(6);
var V8_VERSION = __webpack_require__(147);

var IS_CONCAT_SPREADABLE = wellKnownSymbol('isConcatSpreadable');
var MAX_SAFE_INTEGER = 0x1FFFFFFFFFFFFF;
var MAXIMUM_ALLOWED_INDEX_EXCEEDED = 'Maximum allowed index exceeded';

// We can't use this feature detection in V8 since it causes
// deoptimization and serious performance degradation
// https://github.com/zloirock/core-js/issues/679
var IS_CONCAT_SPREADABLE_SUPPORT = V8_VERSION >= 51 || !fails(function () {
  var array = [];
  array[IS_CONCAT_SPREADABLE] = false;
  return array.concat()[0] !== array;
});

var SPECIES_SUPPORT = arrayMethodHasSpeciesSupport('concat');

var isConcatSpreadable = function (O) {
  if (!isObject(O)) return false;
  var spreadable = O[IS_CONCAT_SPREADABLE];
  return spreadable !== undefined ? !!spreadable : isArray(O);
};

var FORCED = !IS_CONCAT_SPREADABLE_SUPPORT || !SPECIES_SUPPORT;

// `Array.prototype.concat` method
// https://tc39.github.io/ecma262/#sec-array.prototype.concat
// with adding support of @@isConcatSpreadable and @@species
$({ target: 'Array', proto: true, forced: FORCED }, {
  concat: function concat(arg) { // eslint-disable-line no-unused-vars
    var O = toObject(this);
    var A = arraySpeciesCreate(O, 0);
    var n = 0;
    var i, k, length, len, E;
    for (i = -1, length = arguments.length; i < length; i++) {
      E = i === -1 ? O : arguments[i];
      if (isConcatSpreadable(E)) {
        len = toLength(E.length);
        if (n + len > MAX_SAFE_INTEGER) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED);
        for (k = 0; k < len; k++, n++) if (k in E) createProperty(A, n, E[k]);
      } else {
        if (n >= MAX_SAFE_INTEGER) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED);
        createProperty(A, n++, E);
      }
    }
    A.length = n;
    return A;
  }
});


/***/ }),
/* 210 */
/***/ (function(module, exports, __webpack_require__) {

var getBuiltIn = __webpack_require__(36);

module.exports = getBuiltIn('navigator', 'userAgent') || '';


/***/ }),
/* 211 */
/***/ (function(module, exports, __webpack_require__) {

var $ = __webpack_require__(1);
var fill = __webpack_require__(148);
var addToUnscopables = __webpack_require__(149);

// `Array.prototype.fill` method
// https://tc39.github.io/ecma262/#sec-array.prototype.fill
$({ target: 'Array', proto: true }, {
  fill: fill
});

// https://tc39.github.io/ecma262/#sec-array.prototype-@@unscopables
addToUnscopables('fill');


/***/ }),
/* 212 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $forEach = __webpack_require__(20).forEach;
var arrayMethodIsStrict = __webpack_require__(59);
var arrayMethodUsesToLength = __webpack_require__(31);

var STRICT_METHOD = arrayMethodIsStrict('forEach');
var USES_TO_LENGTH = arrayMethodUsesToLength('forEach');

// `Array.prototype.forEach` method implementation
// https://tc39.github.io/ecma262/#sec-array.prototype.foreach
module.exports = (!STRICT_METHOD || !USES_TO_LENGTH) ? function forEach(callbackfn /* , thisArg */) {
  return $forEach(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
} : [].forEach;


/***/ }),
/* 213 */
/***/ (function(module, exports, __webpack_require__) {

var $ = __webpack_require__(1);
var from = __webpack_require__(315);
var checkCorrectnessOfIteration = __webpack_require__(153);

var INCORRECT_ITERATION = !checkCorrectnessOfIteration(function (iterable) {
  Array.from(iterable);
});

// `Array.from` method
// https://tc39.github.io/ecma262/#sec-array.from
$({ target: 'Array', stat: true, forced: INCORRECT_ITERATION }, {
  from: from
});


/***/ }),
/* 214 */
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__(16);

// call something on iterator step with safe closing on error
module.exports = function (iterator, fn, value, ENTRIES) {
  try {
    return ENTRIES ? fn(anObject(value)[0], value[1]) : fn(value);
  // 7.4.6 IteratorClose(iterator, completion)
  } catch (error) {
    var returnMethod = iterator['return'];
    if (returnMethod !== undefined) anObject(returnMethod.call(iterator));
    throw error;
  }
};


/***/ }),
/* 215 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var $includes = __webpack_require__(93).includes;
var addToUnscopables = __webpack_require__(149);
var arrayMethodUsesToLength = __webpack_require__(31);

var USES_TO_LENGTH = arrayMethodUsesToLength('indexOf', { ACCESSORS: true, 1: 0 });

// `Array.prototype.includes` method
// https://tc39.github.io/ecma262/#sec-array.prototype.includes
$({ target: 'Array', proto: true, forced: !USES_TO_LENGTH }, {
  includes: function includes(el /* , fromIndex = 0 */) {
    return $includes(this, el, arguments.length > 1 ? arguments[1] : undefined);
  }
});

// https://tc39.github.io/ecma262/#sec-array.prototype-@@unscopables
addToUnscopables('includes');


/***/ }),
/* 216 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var createIteratorConstructor = __webpack_require__(316);
var getPrototypeOf = __webpack_require__(60);
var setPrototypeOf = __webpack_require__(45);
var setToStringTag = __webpack_require__(95);
var createNonEnumerableProperty = __webpack_require__(18);
var redefine = __webpack_require__(25);
var wellKnownSymbol = __webpack_require__(6);
var IS_PURE = __webpack_require__(56);
var Iterators = __webpack_require__(97);
var IteratorsCore = __webpack_require__(217);

var IteratorPrototype = IteratorsCore.IteratorPrototype;
var BUGGY_SAFARI_ITERATORS = IteratorsCore.BUGGY_SAFARI_ITERATORS;
var ITERATOR = wellKnownSymbol('iterator');
var KEYS = 'keys';
var VALUES = 'values';
var ENTRIES = 'entries';

var returnThis = function () { return this; };

module.exports = function (Iterable, NAME, IteratorConstructor, next, DEFAULT, IS_SET, FORCED) {
  createIteratorConstructor(IteratorConstructor, NAME, next);

  var getIterationMethod = function (KIND) {
    if (KIND === DEFAULT && defaultIterator) return defaultIterator;
    if (!BUGGY_SAFARI_ITERATORS && KIND in IterablePrototype) return IterablePrototype[KIND];
    switch (KIND) {
      case KEYS: return function keys() { return new IteratorConstructor(this, KIND); };
      case VALUES: return function values() { return new IteratorConstructor(this, KIND); };
      case ENTRIES: return function entries() { return new IteratorConstructor(this, KIND); };
    } return function () { return new IteratorConstructor(this); };
  };

  var TO_STRING_TAG = NAME + ' Iterator';
  var INCORRECT_VALUES_NAME = false;
  var IterablePrototype = Iterable.prototype;
  var nativeIterator = IterablePrototype[ITERATOR]
    || IterablePrototype['@@iterator']
    || DEFAULT && IterablePrototype[DEFAULT];
  var defaultIterator = !BUGGY_SAFARI_ITERATORS && nativeIterator || getIterationMethod(DEFAULT);
  var anyNativeIterator = NAME == 'Array' ? IterablePrototype.entries || nativeIterator : nativeIterator;
  var CurrentIteratorPrototype, methods, KEY;

  // fix native
  if (anyNativeIterator) {
    CurrentIteratorPrototype = getPrototypeOf(anyNativeIterator.call(new Iterable()));
    if (IteratorPrototype !== Object.prototype && CurrentIteratorPrototype.next) {
      if (!IS_PURE && getPrototypeOf(CurrentIteratorPrototype) !== IteratorPrototype) {
        if (setPrototypeOf) {
          setPrototypeOf(CurrentIteratorPrototype, IteratorPrototype);
        } else if (typeof CurrentIteratorPrototype[ITERATOR] != 'function') {
          createNonEnumerableProperty(CurrentIteratorPrototype, ITERATOR, returnThis);
        }
      }
      // Set @@toStringTag to native iterators
      setToStringTag(CurrentIteratorPrototype, TO_STRING_TAG, true, true);
      if (IS_PURE) Iterators[TO_STRING_TAG] = returnThis;
    }
  }

  // fix Array#{values, @@iterator}.name in V8 / FF
  if (DEFAULT == VALUES && nativeIterator && nativeIterator.name !== VALUES) {
    INCORRECT_VALUES_NAME = true;
    defaultIterator = function values() { return nativeIterator.call(this); };
  }

  // define iterator
  if ((!IS_PURE || FORCED) && IterablePrototype[ITERATOR] !== defaultIterator) {
    createNonEnumerableProperty(IterablePrototype, ITERATOR, defaultIterator);
  }
  Iterators[NAME] = defaultIterator;

  // export additional methods
  if (DEFAULT) {
    methods = {
      values: getIterationMethod(VALUES),
      keys: IS_SET ? defaultIterator : getIterationMethod(KEYS),
      entries: getIterationMethod(ENTRIES)
    };
    if (FORCED) for (KEY in methods) {
      if (BUGGY_SAFARI_ITERATORS || INCORRECT_VALUES_NAME || !(KEY in IterablePrototype)) {
        redefine(IterablePrototype, KEY, methods[KEY]);
      }
    } else $({ target: NAME, proto: true, forced: BUGGY_SAFARI_ITERATORS || INCORRECT_VALUES_NAME }, methods);
  }

  return methods;
};


/***/ }),
/* 217 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var getPrototypeOf = __webpack_require__(60);
var createNonEnumerableProperty = __webpack_require__(18);
var has = __webpack_require__(14);
var wellKnownSymbol = __webpack_require__(6);
var IS_PURE = __webpack_require__(56);

var ITERATOR = wellKnownSymbol('iterator');
var BUGGY_SAFARI_ITERATORS = false;

var returnThis = function () { return this; };

// `%IteratorPrototype%` object
// https://tc39.github.io/ecma262/#sec-%iteratorprototype%-object
var IteratorPrototype, PrototypeOfArrayIteratorPrototype, arrayIterator;

if ([].keys) {
  arrayIterator = [].keys();
  // Safari 8 has buggy iterators w/o `next`
  if (!('next' in arrayIterator)) BUGGY_SAFARI_ITERATORS = true;
  else {
    PrototypeOfArrayIteratorPrototype = getPrototypeOf(getPrototypeOf(arrayIterator));
    if (PrototypeOfArrayIteratorPrototype !== Object.prototype) IteratorPrototype = PrototypeOfArrayIteratorPrototype;
  }
}

if (IteratorPrototype == undefined) IteratorPrototype = {};

// 25.1.2.1.1 %IteratorPrototype%[@@iterator]()
if (!IS_PURE && !has(IteratorPrototype, ITERATOR)) {
  createNonEnumerableProperty(IteratorPrototype, ITERATOR, returnThis);
}

module.exports = {
  IteratorPrototype: IteratorPrototype,
  BUGGY_SAFARI_ITERATORS: BUGGY_SAFARI_ITERATORS
};


/***/ }),
/* 218 */
/***/ (function(module, exports, __webpack_require__) {

var fails = __webpack_require__(4);

module.exports = !fails(function () {
  function F() { /* empty */ }
  F.prototype.constructor = null;
  return Object.getPrototypeOf(new F()) !== F.prototype;
});


/***/ }),
/* 219 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var IndexedObject = __webpack_require__(92);
var toIndexedObject = __webpack_require__(27);
var arrayMethodIsStrict = __webpack_require__(59);

var nativeJoin = [].join;

var ES3_STRINGS = IndexedObject != Object;
var STRICT_METHOD = arrayMethodIsStrict('join', ',');

// `Array.prototype.join` method
// https://tc39.github.io/ecma262/#sec-array.prototype.join
$({ target: 'Array', proto: true, forced: ES3_STRINGS || !STRICT_METHOD }, {
  join: function join(separator) {
    return nativeJoin.call(toIndexedObject(this), separator === undefined ? ',' : separator);
  }
});


/***/ }),
/* 220 */
/***/ (function(module, exports) {

module.exports = typeof ArrayBuffer !== 'undefined' && typeof DataView !== 'undefined';


/***/ }),
/* 221 */
/***/ (function(module, exports, __webpack_require__) {

var redefine = __webpack_require__(25);

module.exports = function (target, src, options) {
  for (var key in src) redefine(target, key, src[key], options);
  return target;
};


/***/ }),
/* 222 */
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__(37);
var toLength = __webpack_require__(8);

// `ToIndex` abstract operation
// https://tc39.github.io/ecma262/#sec-toindex
module.exports = function (it) {
  if (it === undefined) return 0;
  var number = toInteger(it);
  var length = toLength(number);
  if (number !== length) throw RangeError('Wrong length or index');
  return length;
};


/***/ }),
/* 223 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var DESCRIPTORS = __webpack_require__(11);
var global = __webpack_require__(3);
var isForced = __webpack_require__(144);
var redefine = __webpack_require__(25);
var has = __webpack_require__(14);
var classof = __webpack_require__(30);
var inheritIfRequired = __webpack_require__(224);
var toPrimitive = __webpack_require__(55);
var fails = __webpack_require__(4);
var create = __webpack_require__(58);
var getOwnPropertyNames = __webpack_require__(57).f;
var getOwnPropertyDescriptor = __webpack_require__(54).f;
var defineProperty = __webpack_require__(15).f;
var trim = __webpack_require__(225).trim;

var NUMBER = 'Number';
var NativeNumber = global[NUMBER];
var NumberPrototype = NativeNumber.prototype;

// Opera ~12 has broken Object#toString
var BROKEN_CLASSOF = classof(create(NumberPrototype)) == NUMBER;

// `ToNumber` abstract operation
// https://tc39.github.io/ecma262/#sec-tonumber
var toNumber = function (argument) {
  var it = toPrimitive(argument, false);
  var first, third, radix, maxCode, digits, length, index, code;
  if (typeof it == 'string' && it.length > 2) {
    it = trim(it);
    first = it.charCodeAt(0);
    if (first === 43 || first === 45) {
      third = it.charCodeAt(2);
      if (third === 88 || third === 120) return NaN; // Number('+0x1') should be NaN, old V8 fix
    } else if (first === 48) {
      switch (it.charCodeAt(1)) {
        case 66: case 98: radix = 2; maxCode = 49; break; // fast equal of /^0b[01]+$/i
        case 79: case 111: radix = 8; maxCode = 55; break; // fast equal of /^0o[0-7]+$/i
        default: return +it;
      }
      digits = it.slice(2);
      length = digits.length;
      for (index = 0; index < length; index++) {
        code = digits.charCodeAt(index);
        // parseInt parses a string to a first unavailable symbol
        // but ToNumber should return NaN if a string contains unavailable symbols
        if (code < 48 || code > maxCode) return NaN;
      } return parseInt(digits, radix);
    }
  } return +it;
};

// `Number` constructor
// https://tc39.github.io/ecma262/#sec-number-constructor
if (isForced(NUMBER, !NativeNumber(' 0o1') || !NativeNumber('0b1') || NativeNumber('+0x1'))) {
  var NumberWrapper = function Number(value) {
    var it = arguments.length < 1 ? 0 : value;
    var dummy = this;
    return dummy instanceof NumberWrapper
      // check on 1..constructor(foo) case
      && (BROKEN_CLASSOF ? fails(function () { NumberPrototype.valueOf.call(dummy); }) : classof(dummy) != NUMBER)
        ? inheritIfRequired(new NativeNumber(toNumber(it)), dummy, NumberWrapper) : toNumber(it);
  };
  for (var keys = DESCRIPTORS ? getOwnPropertyNames(NativeNumber) : (
    // ES3:
    'MAX_VALUE,MIN_VALUE,NaN,NEGATIVE_INFINITY,POSITIVE_INFINITY,' +
    // ES2015 (in case, if modules with ES2015 Number statics required before):
    'EPSILON,isFinite,isInteger,isNaN,isSafeInteger,MAX_SAFE_INTEGER,' +
    'MIN_SAFE_INTEGER,parseFloat,parseInt,isInteger'
  ).split(','), j = 0, key; keys.length > j; j++) {
    if (has(NativeNumber, key = keys[j]) && !has(NumberWrapper, key)) {
      defineProperty(NumberWrapper, key, getOwnPropertyDescriptor(NativeNumber, key));
    }
  }
  NumberWrapper.prototype = NumberPrototype;
  NumberPrototype.constructor = NumberWrapper;
  redefine(global, NUMBER, NumberWrapper);
}


/***/ }),
/* 224 */
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__(13);
var setPrototypeOf = __webpack_require__(45);

// makes subclassing work correct for wrapped built-ins
module.exports = function ($this, dummy, Wrapper) {
  var NewTarget, NewTargetPrototype;
  if (
    // it can work only with native `setPrototypeOf`
    setPrototypeOf &&
    // we haven't completely correct pre-ES6 way for getting `new.target`, so use this
    typeof (NewTarget = dummy.constructor) == 'function' &&
    NewTarget !== Wrapper &&
    isObject(NewTargetPrototype = NewTarget.prototype) &&
    NewTargetPrototype !== Wrapper.prototype
  ) setPrototypeOf($this, NewTargetPrototype);
  return $this;
};


/***/ }),
/* 225 */
/***/ (function(module, exports, __webpack_require__) {

var requireObjectCoercible = __webpack_require__(34);
var whitespaces = __webpack_require__(226);

var whitespace = '[' + whitespaces + ']';
var ltrim = RegExp('^' + whitespace + whitespace + '*');
var rtrim = RegExp(whitespace + whitespace + '*$');

// `String.prototype.{ trim, trimStart, trimEnd, trimLeft, trimRight }` methods implementation
var createMethod = function (TYPE) {
  return function ($this) {
    var string = String(requireObjectCoercible($this));
    if (TYPE & 1) string = string.replace(ltrim, '');
    if (TYPE & 2) string = string.replace(rtrim, '');
    return string;
  };
};

module.exports = {
  // `String.prototype.{ trimLeft, trimStart }` methods
  // https://tc39.github.io/ecma262/#sec-string.prototype.trimstart
  start: createMethod(1),
  // `String.prototype.{ trimRight, trimEnd }` methods
  // https://tc39.github.io/ecma262/#sec-string.prototype.trimend
  end: createMethod(2),
  // `String.prototype.trim` method
  // https://tc39.github.io/ecma262/#sec-string.prototype.trim
  trim: createMethod(3)
};


/***/ }),
/* 226 */
/***/ (function(module, exports) {

// a string of all valid unicode whitespaces
// eslint-disable-next-line max-len
module.exports = '\u0009\u000A\u000B\u000C\u000D\u0020\u00A0\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200A\u202F\u205F\u3000\u2028\u2029\uFEFF';


/***/ }),
/* 227 */
/***/ (function(module, exports, __webpack_require__) {

var $ = __webpack_require__(1);
var toObject = __webpack_require__(19);
var nativeKeys = __webpack_require__(94);
var fails = __webpack_require__(4);

var FAILS_ON_PRIMITIVES = fails(function () { nativeKeys(1); });

// `Object.keys` method
// https://tc39.github.io/ecma262/#sec-object.keys
$({ target: 'Object', stat: true, forced: FAILS_ON_PRIMITIVES }, {
  keys: function keys(it) {
    return nativeKeys(toObject(it));
  }
});


/***/ }),
/* 228 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var anObject = __webpack_require__(16);

// `RegExp.prototype.flags` getter implementation
// https://tc39.github.io/ecma262/#sec-get-regexp.prototype.flags
module.exports = function () {
  var that = anObject(this);
  var result = '';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.dotAll) result += 's';
  if (that.unicode) result += 'u';
  if (that.sticky) result += 'y';
  return result;
};


/***/ }),
/* 229 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var redefine = __webpack_require__(25);
var anObject = __webpack_require__(16);
var fails = __webpack_require__(4);
var flags = __webpack_require__(228);

var TO_STRING = 'toString';
var RegExpPrototype = RegExp.prototype;
var nativeToString = RegExpPrototype[TO_STRING];

var NOT_GENERIC = fails(function () { return nativeToString.call({ source: 'a', flags: 'b' }) != '/a/b'; });
// FF44- RegExp#toString has a wrong name
var INCORRECT_NAME = nativeToString.name != TO_STRING;

// `RegExp.prototype.toString` method
// https://tc39.github.io/ecma262/#sec-regexp.prototype.tostring
if (NOT_GENERIC || INCORRECT_NAME) {
  redefine(RegExp.prototype, TO_STRING, function toString() {
    var R = anObject(this);
    var p = String(R.source);
    var rf = R.flags;
    var f = String(rf === undefined && R instanceof RegExp && !('flags' in RegExpPrototype) ? flags.call(R) : rf);
    return '/' + p + '/' + f;
  }, { unsafe: true });
}


/***/ }),
/* 230 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var fixRegExpWellKnownSymbolLogic = __webpack_require__(161);
var anObject = __webpack_require__(16);
var toObject = __webpack_require__(19);
var toLength = __webpack_require__(8);
var toInteger = __webpack_require__(37);
var requireObjectCoercible = __webpack_require__(34);
var advanceStringIndex = __webpack_require__(162);
var regExpExec = __webpack_require__(163);

var max = Math.max;
var min = Math.min;
var floor = Math.floor;
var SUBSTITUTION_SYMBOLS = /\$([$&'`]|\d\d?|<[^>]*>)/g;
var SUBSTITUTION_SYMBOLS_NO_NAMED = /\$([$&'`]|\d\d?)/g;

var maybeToString = function (it) {
  return it === undefined ? it : String(it);
};

// @@replace logic
fixRegExpWellKnownSymbolLogic('replace', 2, function (REPLACE, nativeReplace, maybeCallNative, reason) {
  var REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE = reason.REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE;
  var REPLACE_KEEPS_$0 = reason.REPLACE_KEEPS_$0;
  var UNSAFE_SUBSTITUTE = REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE ? '$' : '$0';

  return [
    // `String.prototype.replace` method
    // https://tc39.github.io/ecma262/#sec-string.prototype.replace
    function replace(searchValue, replaceValue) {
      var O = requireObjectCoercible(this);
      var replacer = searchValue == undefined ? undefined : searchValue[REPLACE];
      return replacer !== undefined
        ? replacer.call(searchValue, O, replaceValue)
        : nativeReplace.call(String(O), searchValue, replaceValue);
    },
    // `RegExp.prototype[@@replace]` method
    // https://tc39.github.io/ecma262/#sec-regexp.prototype-@@replace
    function (regexp, replaceValue) {
      if (
        (!REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE && REPLACE_KEEPS_$0) ||
        (typeof replaceValue === 'string' && replaceValue.indexOf(UNSAFE_SUBSTITUTE) === -1)
      ) {
        var res = maybeCallNative(nativeReplace, regexp, this, replaceValue);
        if (res.done) return res.value;
      }

      var rx = anObject(regexp);
      var S = String(this);

      var functionalReplace = typeof replaceValue === 'function';
      if (!functionalReplace) replaceValue = String(replaceValue);

      var global = rx.global;
      if (global) {
        var fullUnicode = rx.unicode;
        rx.lastIndex = 0;
      }
      var results = [];
      while (true) {
        var result = regExpExec(rx, S);
        if (result === null) break;

        results.push(result);
        if (!global) break;

        var matchStr = String(result[0]);
        if (matchStr === '') rx.lastIndex = advanceStringIndex(S, toLength(rx.lastIndex), fullUnicode);
      }

      var accumulatedResult = '';
      var nextSourcePosition = 0;
      for (var i = 0; i < results.length; i++) {
        result = results[i];

        var matched = String(result[0]);
        var position = max(min(toInteger(result.index), S.length), 0);
        var captures = [];
        // NOTE: This is equivalent to
        //   captures = result.slice(1).map(maybeToString)
        // but for some reason `nativeSlice.call(result, 1, result.length)` (called in
        // the slice polyfill when slicing native arrays) "doesn't work" in safari 9 and
        // causes a crash (https://pastebin.com/N21QzeQA) when trying to debug it.
        for (var j = 1; j < result.length; j++) captures.push(maybeToString(result[j]));
        var namedCaptures = result.groups;
        if (functionalReplace) {
          var replacerArgs = [matched].concat(captures, position, S);
          if (namedCaptures !== undefined) replacerArgs.push(namedCaptures);
          var replacement = String(replaceValue.apply(undefined, replacerArgs));
        } else {
          replacement = getSubstitution(matched, S, position, captures, namedCaptures, replaceValue);
        }
        if (position >= nextSourcePosition) {
          accumulatedResult += S.slice(nextSourcePosition, position) + replacement;
          nextSourcePosition = position + matched.length;
        }
      }
      return accumulatedResult + S.slice(nextSourcePosition);
    }
  ];

  // https://tc39.github.io/ecma262/#sec-getsubstitution
  function getSubstitution(matched, str, position, captures, namedCaptures, replacement) {
    var tailPos = position + matched.length;
    var m = captures.length;
    var symbols = SUBSTITUTION_SYMBOLS_NO_NAMED;
    if (namedCaptures !== undefined) {
      namedCaptures = toObject(namedCaptures);
      symbols = SUBSTITUTION_SYMBOLS;
    }
    return nativeReplace.call(replacement, symbols, function (match, ch) {
      var capture;
      switch (ch.charAt(0)) {
        case '$': return '$';
        case '&': return matched;
        case '`': return str.slice(0, position);
        case "'": return str.slice(tailPos);
        case '<':
          capture = namedCaptures[ch.slice(1, -1)];
          break;
        default: // \d\d?
          var n = +ch;
          if (n === 0) return match;
          if (n > m) {
            var f = floor(n / 10);
            if (f === 0) return match;
            if (f <= m) return captures[f - 1] === undefined ? ch.charAt(1) : captures[f - 1] + ch.charAt(1);
            return match;
          }
          capture = captures[n - 1];
      }
      return capture === undefined ? '' : capture;
    });
  }
});


/***/ }),
/* 231 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var fixRegExpWellKnownSymbolLogic = __webpack_require__(161);
var isRegExp = __webpack_require__(332);
var anObject = __webpack_require__(16);
var requireObjectCoercible = __webpack_require__(34);
var speciesConstructor = __webpack_require__(38);
var advanceStringIndex = __webpack_require__(162);
var toLength = __webpack_require__(8);
var callRegExpExec = __webpack_require__(163);
var regexpExec = __webpack_require__(119);
var fails = __webpack_require__(4);

var arrayPush = [].push;
var min = Math.min;
var MAX_UINT32 = 0xFFFFFFFF;

// babel-minify transpiles RegExp('x', 'y') -> /x/y and it causes SyntaxError
var SUPPORTS_Y = !fails(function () { return !RegExp(MAX_UINT32, 'y'); });

// @@split logic
fixRegExpWellKnownSymbolLogic('split', 2, function (SPLIT, nativeSplit, maybeCallNative) {
  var internalSplit;
  if (
    'abbc'.split(/(b)*/)[1] == 'c' ||
    'test'.split(/(?:)/, -1).length != 4 ||
    'ab'.split(/(?:ab)*/).length != 2 ||
    '.'.split(/(.?)(.?)/).length != 4 ||
    '.'.split(/()()/).length > 1 ||
    ''.split(/.?/).length
  ) {
    // based on es5-shim implementation, need to rework it
    internalSplit = function (separator, limit) {
      var string = String(requireObjectCoercible(this));
      var lim = limit === undefined ? MAX_UINT32 : limit >>> 0;
      if (lim === 0) return [];
      if (separator === undefined) return [string];
      // If `separator` is not a regex, use native split
      if (!isRegExp(separator)) {
        return nativeSplit.call(string, separator, lim);
      }
      var output = [];
      var flags = (separator.ignoreCase ? 'i' : '') +
                  (separator.multiline ? 'm' : '') +
                  (separator.unicode ? 'u' : '') +
                  (separator.sticky ? 'y' : '');
      var lastLastIndex = 0;
      // Make `global` and avoid `lastIndex` issues by working with a copy
      var separatorCopy = new RegExp(separator.source, flags + 'g');
      var match, lastIndex, lastLength;
      while (match = regexpExec.call(separatorCopy, string)) {
        lastIndex = separatorCopy.lastIndex;
        if (lastIndex > lastLastIndex) {
          output.push(string.slice(lastLastIndex, match.index));
          if (match.length > 1 && match.index < string.length) arrayPush.apply(output, match.slice(1));
          lastLength = match[0].length;
          lastLastIndex = lastIndex;
          if (output.length >= lim) break;
        }
        if (separatorCopy.lastIndex === match.index) separatorCopy.lastIndex++; // Avoid an infinite loop
      }
      if (lastLastIndex === string.length) {
        if (lastLength || !separatorCopy.test('')) output.push('');
      } else output.push(string.slice(lastLastIndex));
      return output.length > lim ? output.slice(0, lim) : output;
    };
  // Chakra, V8
  } else if ('0'.split(undefined, 0).length) {
    internalSplit = function (separator, limit) {
      return separator === undefined && limit === 0 ? [] : nativeSplit.call(this, separator, limit);
    };
  } else internalSplit = nativeSplit;

  return [
    // `String.prototype.split` method
    // https://tc39.github.io/ecma262/#sec-string.prototype.split
    function split(separator, limit) {
      var O = requireObjectCoercible(this);
      var splitter = separator == undefined ? undefined : separator[SPLIT];
      return splitter !== undefined
        ? splitter.call(separator, O, limit)
        : internalSplit.call(String(O), separator, limit);
    },
    // `RegExp.prototype[@@split]` method
    // https://tc39.github.io/ecma262/#sec-regexp.prototype-@@split
    //
    // NOTE: This cannot be properly polyfilled in engines that don't support
    // the 'y' flag.
    function (regexp, limit) {
      var res = maybeCallNative(internalSplit, regexp, this, limit, internalSplit !== nativeSplit);
      if (res.done) return res.value;

      var rx = anObject(regexp);
      var S = String(this);
      var C = speciesConstructor(rx, RegExp);

      var unicodeMatching = rx.unicode;
      var flags = (rx.ignoreCase ? 'i' : '') +
                  (rx.multiline ? 'm' : '') +
                  (rx.unicode ? 'u' : '') +
                  (SUPPORTS_Y ? 'y' : 'g');

      // ^(? + rx + ) is needed, in combination with some S slicing, to
      // simulate the 'y' flag.
      var splitter = new C(SUPPORTS_Y ? rx : '^(?:' + rx.source + ')', flags);
      var lim = limit === undefined ? MAX_UINT32 : limit >>> 0;
      if (lim === 0) return [];
      if (S.length === 0) return callRegExpExec(splitter, S) === null ? [S] : [];
      var p = 0;
      var q = 0;
      var A = [];
      while (q < S.length) {
        splitter.lastIndex = SUPPORTS_Y ? q : 0;
        var z = callRegExpExec(splitter, SUPPORTS_Y ? S : S.slice(q));
        var e;
        if (
          z === null ||
          (e = min(toLength(splitter.lastIndex + (SUPPORTS_Y ? 0 : q)), S.length)) === p
        ) {
          q = advanceStringIndex(S, q, unicodeMatching);
        } else {
          A.push(S.slice(p, q));
          if (A.length === lim) return A;
          for (var i = 1; i <= z.length - 1; i++) {
            A.push(z[i]);
            if (A.length === lim) return A;
          }
          q = p = e;
        }
      }
      A.push(S.slice(p));
      return A;
    }
  ];
}, !SUPPORTS_Y);


/***/ }),
/* 232 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var $trim = __webpack_require__(225).trim;
var forcedStringTrimMethod = __webpack_require__(333);

// `String.prototype.trim` method
// https://tc39.github.io/ecma262/#sec-string.prototype.trim
$({ target: 'String', proto: true, forced: forcedStringTrimMethod('trim') }, {
  trim: function trim() {
    return $trim(this);
  }
});


/***/ }),
/* 233 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var createHTML = __webpack_require__(234);
var forcedStringHTMLMethod = __webpack_require__(235);

// `String.prototype.link` method
// https://tc39.github.io/ecma262/#sec-string.prototype.link
$({ target: 'String', proto: true, forced: forcedStringHTMLMethod('link') }, {
  link: function link(url) {
    return createHTML(this, 'a', 'href', url);
  }
});


/***/ }),
/* 234 */
/***/ (function(module, exports, __webpack_require__) {

var requireObjectCoercible = __webpack_require__(34);

var quot = /"/g;

// B.2.3.2.1 CreateHTML(string, tag, attribute, value)
// https://tc39.github.io/ecma262/#sec-createhtml
module.exports = function (string, tag, attribute, value) {
  var S = String(requireObjectCoercible(string));
  var p1 = '<' + tag;
  if (attribute !== '') p1 += ' ' + attribute + '="' + String(value).replace(quot, '&quot;') + '"';
  return p1 + '>' + S + '</' + tag + '>';
};


/***/ }),
/* 235 */
/***/ (function(module, exports, __webpack_require__) {

var fails = __webpack_require__(4);

// check the existence of a method, lowercase
// of a tag and escaping quotes in arguments
module.exports = function (METHOD_NAME) {
  return fails(function () {
    var test = ''[METHOD_NAME]('"');
    return test !== test.toLowerCase() || test.split('"').length > 3;
  });
};


/***/ }),
/* 236 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var global = __webpack_require__(3);
var DESCRIPTORS = __webpack_require__(11);
var TYPED_ARRAYS_CONSTRUCTORS_REQUIRES_WRAPPERS = __webpack_require__(335);
var ArrayBufferViewCore = __webpack_require__(5);
var ArrayBufferModule = __webpack_require__(154);
var anInstance = __webpack_require__(155);
var createPropertyDescriptor = __webpack_require__(42);
var createNonEnumerableProperty = __webpack_require__(18);
var toLength = __webpack_require__(8);
var toIndex = __webpack_require__(222);
var toOffset = __webpack_require__(237);
var toPrimitive = __webpack_require__(55);
var has = __webpack_require__(14);
var classof = __webpack_require__(117);
var isObject = __webpack_require__(13);
var create = __webpack_require__(58);
var setPrototypeOf = __webpack_require__(45);
var getOwnPropertyNames = __webpack_require__(57).f;
var typedArrayFrom = __webpack_require__(337);
var forEach = __webpack_require__(20).forEach;
var setSpecies = __webpack_require__(156);
var definePropertyModule = __webpack_require__(15);
var getOwnPropertyDescriptorModule = __webpack_require__(54);
var InternalStateModule = __webpack_require__(35);
var inheritIfRequired = __webpack_require__(224);

var getInternalState = InternalStateModule.get;
var setInternalState = InternalStateModule.set;
var nativeDefineProperty = definePropertyModule.f;
var nativeGetOwnPropertyDescriptor = getOwnPropertyDescriptorModule.f;
var round = Math.round;
var RangeError = global.RangeError;
var ArrayBuffer = ArrayBufferModule.ArrayBuffer;
var DataView = ArrayBufferModule.DataView;
var NATIVE_ARRAY_BUFFER_VIEWS = ArrayBufferViewCore.NATIVE_ARRAY_BUFFER_VIEWS;
var TYPED_ARRAY_TAG = ArrayBufferViewCore.TYPED_ARRAY_TAG;
var TypedArray = ArrayBufferViewCore.TypedArray;
var TypedArrayPrototype = ArrayBufferViewCore.TypedArrayPrototype;
var aTypedArrayConstructor = ArrayBufferViewCore.aTypedArrayConstructor;
var isTypedArray = ArrayBufferViewCore.isTypedArray;
var BYTES_PER_ELEMENT = 'BYTES_PER_ELEMENT';
var WRONG_LENGTH = 'Wrong length';

var fromList = function (C, list) {
  var index = 0;
  var length = list.length;
  var result = new (aTypedArrayConstructor(C))(length);
  while (length > index) result[index] = list[index++];
  return result;
};

var addGetter = function (it, key) {
  nativeDefineProperty(it, key, { get: function () {
    return getInternalState(this)[key];
  } });
};

var isArrayBuffer = function (it) {
  var klass;
  return it instanceof ArrayBuffer || (klass = classof(it)) == 'ArrayBuffer' || klass == 'SharedArrayBuffer';
};

var isTypedArrayIndex = function (target, key) {
  return isTypedArray(target)
    && typeof key != 'symbol'
    && key in target
    && String(+key) == String(key);
};

var wrappedGetOwnPropertyDescriptor = function getOwnPropertyDescriptor(target, key) {
  return isTypedArrayIndex(target, key = toPrimitive(key, true))
    ? createPropertyDescriptor(2, target[key])
    : nativeGetOwnPropertyDescriptor(target, key);
};

var wrappedDefineProperty = function defineProperty(target, key, descriptor) {
  if (isTypedArrayIndex(target, key = toPrimitive(key, true))
    && isObject(descriptor)
    && has(descriptor, 'value')
    && !has(descriptor, 'get')
    && !has(descriptor, 'set')
    // TODO: add validation descriptor w/o calling accessors
    && !descriptor.configurable
    && (!has(descriptor, 'writable') || descriptor.writable)
    && (!has(descriptor, 'enumerable') || descriptor.enumerable)
  ) {
    target[key] = descriptor.value;
    return target;
  } return nativeDefineProperty(target, key, descriptor);
};

if (DESCRIPTORS) {
  if (!NATIVE_ARRAY_BUFFER_VIEWS) {
    getOwnPropertyDescriptorModule.f = wrappedGetOwnPropertyDescriptor;
    definePropertyModule.f = wrappedDefineProperty;
    addGetter(TypedArrayPrototype, 'buffer');
    addGetter(TypedArrayPrototype, 'byteOffset');
    addGetter(TypedArrayPrototype, 'byteLength');
    addGetter(TypedArrayPrototype, 'length');
  }

  $({ target: 'Object', stat: true, forced: !NATIVE_ARRAY_BUFFER_VIEWS }, {
    getOwnPropertyDescriptor: wrappedGetOwnPropertyDescriptor,
    defineProperty: wrappedDefineProperty
  });

  module.exports = function (TYPE, wrapper, CLAMPED) {
    var BYTES = TYPE.match(/\d+$/)[0] / 8;
    var CONSTRUCTOR_NAME = TYPE + (CLAMPED ? 'Clamped' : '') + 'Array';
    var GETTER = 'get' + TYPE;
    var SETTER = 'set' + TYPE;
    var NativeTypedArrayConstructor = global[CONSTRUCTOR_NAME];
    var TypedArrayConstructor = NativeTypedArrayConstructor;
    var TypedArrayConstructorPrototype = TypedArrayConstructor && TypedArrayConstructor.prototype;
    var exported = {};

    var getter = function (that, index) {
      var data = getInternalState(that);
      return data.view[GETTER](index * BYTES + data.byteOffset, true);
    };

    var setter = function (that, index, value) {
      var data = getInternalState(that);
      if (CLAMPED) value = (value = round(value)) < 0 ? 0 : value > 0xFF ? 0xFF : value & 0xFF;
      data.view[SETTER](index * BYTES + data.byteOffset, value, true);
    };

    var addElement = function (that, index) {
      nativeDefineProperty(that, index, {
        get: function () {
          return getter(this, index);
        },
        set: function (value) {
          return setter(this, index, value);
        },
        enumerable: true
      });
    };

    if (!NATIVE_ARRAY_BUFFER_VIEWS) {
      TypedArrayConstructor = wrapper(function (that, data, offset, $length) {
        anInstance(that, TypedArrayConstructor, CONSTRUCTOR_NAME);
        var index = 0;
        var byteOffset = 0;
        var buffer, byteLength, length;
        if (!isObject(data)) {
          length = toIndex(data);
          byteLength = length * BYTES;
          buffer = new ArrayBuffer(byteLength);
        } else if (isArrayBuffer(data)) {
          buffer = data;
          byteOffset = toOffset(offset, BYTES);
          var $len = data.byteLength;
          if ($length === undefined) {
            if ($len % BYTES) throw RangeError(WRONG_LENGTH);
            byteLength = $len - byteOffset;
            if (byteLength < 0) throw RangeError(WRONG_LENGTH);
          } else {
            byteLength = toLength($length) * BYTES;
            if (byteLength + byteOffset > $len) throw RangeError(WRONG_LENGTH);
          }
          length = byteLength / BYTES;
        } else if (isTypedArray(data)) {
          return fromList(TypedArrayConstructor, data);
        } else {
          return typedArrayFrom.call(TypedArrayConstructor, data);
        }
        setInternalState(that, {
          buffer: buffer,
          byteOffset: byteOffset,
          byteLength: byteLength,
          length: length,
          view: new DataView(buffer)
        });
        while (index < length) addElement(that, index++);
      });

      if (setPrototypeOf) setPrototypeOf(TypedArrayConstructor, TypedArray);
      TypedArrayConstructorPrototype = TypedArrayConstructor.prototype = create(TypedArrayPrototype);
    } else if (TYPED_ARRAYS_CONSTRUCTORS_REQUIRES_WRAPPERS) {
      TypedArrayConstructor = wrapper(function (dummy, data, typedArrayOffset, $length) {
        anInstance(dummy, TypedArrayConstructor, CONSTRUCTOR_NAME);
        return inheritIfRequired(function () {
          if (!isObject(data)) return new NativeTypedArrayConstructor(toIndex(data));
          if (isArrayBuffer(data)) return $length !== undefined
            ? new NativeTypedArrayConstructor(data, toOffset(typedArrayOffset, BYTES), $length)
            : typedArrayOffset !== undefined
              ? new NativeTypedArrayConstructor(data, toOffset(typedArrayOffset, BYTES))
              : new NativeTypedArrayConstructor(data);
          if (isTypedArray(data)) return fromList(TypedArrayConstructor, data);
          return typedArrayFrom.call(TypedArrayConstructor, data);
        }(), dummy, TypedArrayConstructor);
      });

      if (setPrototypeOf) setPrototypeOf(TypedArrayConstructor, TypedArray);
      forEach(getOwnPropertyNames(NativeTypedArrayConstructor), function (key) {
        if (!(key in TypedArrayConstructor)) {
          createNonEnumerableProperty(TypedArrayConstructor, key, NativeTypedArrayConstructor[key]);
        }
      });
      TypedArrayConstructor.prototype = TypedArrayConstructorPrototype;
    }

    if (TypedArrayConstructorPrototype.constructor !== TypedArrayConstructor) {
      createNonEnumerableProperty(TypedArrayConstructorPrototype, 'constructor', TypedArrayConstructor);
    }

    if (TYPED_ARRAY_TAG) {
      createNonEnumerableProperty(TypedArrayConstructorPrototype, TYPED_ARRAY_TAG, CONSTRUCTOR_NAME);
    }

    exported[CONSTRUCTOR_NAME] = TypedArrayConstructor;

    $({
      global: true, forced: TypedArrayConstructor != NativeTypedArrayConstructor, sham: !NATIVE_ARRAY_BUFFER_VIEWS
    }, exported);

    if (!(BYTES_PER_ELEMENT in TypedArrayConstructor)) {
      createNonEnumerableProperty(TypedArrayConstructor, BYTES_PER_ELEMENT, BYTES);
    }

    if (!(BYTES_PER_ELEMENT in TypedArrayConstructorPrototype)) {
      createNonEnumerableProperty(TypedArrayConstructorPrototype, BYTES_PER_ELEMENT, BYTES);
    }

    setSpecies(CONSTRUCTOR_NAME);
  };
} else module.exports = function () { /* empty */ };


/***/ }),
/* 237 */
/***/ (function(module, exports, __webpack_require__) {

var toPositiveInteger = __webpack_require__(336);

module.exports = function (it, BYTES) {
  var offset = toPositiveInteger(it);
  if (offset % BYTES) throw RangeError('Wrong offset');
  return offset;
};


/***/ }),
/* 238 */
/***/ (function(module, exports, __webpack_require__) {

var aFunction = __webpack_require__(44);
var toObject = __webpack_require__(19);
var IndexedObject = __webpack_require__(92);
var toLength = __webpack_require__(8);

// `Array.prototype.{ reduce, reduceRight }` methods implementation
var createMethod = function (IS_RIGHT) {
  return function (that, callbackfn, argumentsLength, memo) {
    aFunction(callbackfn);
    var O = toObject(that);
    var self = IndexedObject(O);
    var length = toLength(O.length);
    var index = IS_RIGHT ? length - 1 : 0;
    var i = IS_RIGHT ? -1 : 1;
    if (argumentsLength < 2) while (true) {
      if (index in self) {
        memo = self[index];
        index += i;
        break;
      }
      index += i;
      if (IS_RIGHT ? index < 0 : length <= index) {
        throw TypeError('Reduce of empty array with no initial value');
      }
    }
    for (;IS_RIGHT ? index >= 0 : length > index; index += i) if (index in self) {
      memo = callbackfn(memo, self[index], index, O);
    }
    return memo;
  };
};

module.exports = {
  // `Array.prototype.reduce` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.reduce
  left: createMethod(false),
  // `Array.prototype.reduceRight` method
  // https://tc39.github.io/ecma262/#sec-array.prototype.reduceright
  right: createMethod(true)
};


/***/ }),
/* 239 */
/***/ (function(module, exports) {

// iterable DOM collections
// flag - `iterable` interface - 'entries', 'keys', 'values', 'forEach' methods
module.exports = {
  CSSRuleList: 0,
  CSSStyleDeclaration: 0,
  CSSValueList: 0,
  ClientRectList: 0,
  DOMRectList: 0,
  DOMStringList: 0,
  DOMTokenList: 1,
  DataTransferItemList: 0,
  FileList: 0,
  HTMLAllCollection: 0,
  HTMLCollection: 0,
  HTMLFormElement: 0,
  HTMLSelectElement: 0,
  MediaList: 0,
  MimeTypeArray: 0,
  NamedNodeMap: 0,
  NodeList: 1,
  PaintRequestList: 0,
  Plugin: 0,
  PluginArray: 0,
  SVGLengthList: 0,
  SVGNumberList: 0,
  SVGPathSegList: 0,
  SVGPointList: 0,
  SVGStringList: 0,
  SVGTransformList: 0,
  SourceBufferList: 0,
  StyleSheetList: 0,
  TextTrackCueList: 0,
  TextTrackList: 0,
  TouchList: 0
};


/***/ }),
/* 240 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(global, process) {// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.



/*<replacement>*/

var pna = __webpack_require__(123);
/*</replacement>*/

module.exports = Readable;

/*<replacement>*/
var isArray = __webpack_require__(196);
/*</replacement>*/

/*<replacement>*/
var Duplex;
/*</replacement>*/

Readable.ReadableState = ReadableState;

/*<replacement>*/
var EE = __webpack_require__(122).EventEmitter;

var EElistenerCount = function (emitter, type) {
  return emitter.listeners(type).length;
};
/*</replacement>*/

/*<replacement>*/
var Stream = __webpack_require__(241);
/*</replacement>*/

/*<replacement>*/

var Buffer = __webpack_require__(124).Buffer;
var OurUint8Array = global.Uint8Array || function () {};
function _uint8ArrayToBuffer(chunk) {
  return Buffer.from(chunk);
}
function _isUint8Array(obj) {
  return Buffer.isBuffer(obj) || obj instanceof OurUint8Array;
}

/*</replacement>*/

/*<replacement>*/
var util = Object.create(__webpack_require__(98));
util.inherits = __webpack_require__(86);
/*</replacement>*/

/*<replacement>*/
var debugUtil = __webpack_require__(341);
var debug = void 0;
if (debugUtil && debugUtil.debuglog) {
  debug = debugUtil.debuglog('stream');
} else {
  debug = function () {};
}
/*</replacement>*/

var BufferList = __webpack_require__(342);
var destroyImpl = __webpack_require__(242);
var StringDecoder;

util.inherits(Readable, Stream);

var kProxyEvents = ['error', 'close', 'destroy', 'pause', 'resume'];

function prependListener(emitter, event, fn) {
  // Sadly this is not cacheable as some libraries bundle their own
  // event emitter implementation with them.
  if (typeof emitter.prependListener === 'function') return emitter.prependListener(event, fn);

  // This is a hack to make sure that our error handler is attached before any
  // userland ones.  NEVER DO THIS. This is here only because this code needs
  // to continue to work with older versions of Node.js that do not include
  // the prependListener() method. The goal is to eventually remove this hack.
  if (!emitter._events || !emitter._events[event]) emitter.on(event, fn);else if (isArray(emitter._events[event])) emitter._events[event].unshift(fn);else emitter._events[event] = [fn, emitter._events[event]];
}

function ReadableState(options, stream) {
  Duplex = Duplex || __webpack_require__(48);

  options = options || {};

  // Duplex streams are both readable and writable, but share
  // the same options object.
  // However, some cases require setting options to different
  // values for the readable and the writable sides of the duplex stream.
  // These options can be provided separately as readableXXX and writableXXX.
  var isDuplex = stream instanceof Duplex;

  // object stream flag. Used to make read(n) ignore n and to
  // make all the buffer merging and length checks go away
  this.objectMode = !!options.objectMode;

  if (isDuplex) this.objectMode = this.objectMode || !!options.readableObjectMode;

  // the point at which it stops calling _read() to fill the buffer
  // Note: 0 is a valid value, means "don't call _read preemptively ever"
  var hwm = options.highWaterMark;
  var readableHwm = options.readableHighWaterMark;
  var defaultHwm = this.objectMode ? 16 : 16 * 1024;

  if (hwm || hwm === 0) this.highWaterMark = hwm;else if (isDuplex && (readableHwm || readableHwm === 0)) this.highWaterMark = readableHwm;else this.highWaterMark = defaultHwm;

  // cast to ints.
  this.highWaterMark = Math.floor(this.highWaterMark);

  // A linked list is used to store data chunks instead of an array because the
  // linked list can remove elements from the beginning faster than
  // array.shift()
  this.buffer = new BufferList();
  this.length = 0;
  this.pipes = null;
  this.pipesCount = 0;
  this.flowing = null;
  this.ended = false;
  this.endEmitted = false;
  this.reading = false;

  // a flag to be able to tell if the event 'readable'/'data' is emitted
  // immediately, or on a later tick.  We set this to true at first, because
  // any actions that shouldn't happen until "later" should generally also
  // not happen before the first read call.
  this.sync = true;

  // whenever we return null, then we set a flag to say
  // that we're awaiting a 'readable' event emission.
  this.needReadable = false;
  this.emittedReadable = false;
  this.readableListening = false;
  this.resumeScheduled = false;

  // has it been destroyed
  this.destroyed = false;

  // Crypto is kind of old and crusty.  Historically, its default string
  // encoding is 'binary' so we have to make this configurable.
  // Everything else in the universe uses 'utf8', though.
  this.defaultEncoding = options.defaultEncoding || 'utf8';

  // the number of writers that are awaiting a drain event in .pipe()s
  this.awaitDrain = 0;

  // if true, a maybeReadMore has been scheduled
  this.readingMore = false;

  this.decoder = null;
  this.encoding = null;
  if (options.encoding) {
    if (!StringDecoder) StringDecoder = __webpack_require__(125).StringDecoder;
    this.decoder = new StringDecoder(options.encoding);
    this.encoding = options.encoding;
  }
}

function Readable(options) {
  Duplex = Duplex || __webpack_require__(48);

  if (!(this instanceof Readable)) return new Readable(options);

  this._readableState = new ReadableState(options, this);

  // legacy
  this.readable = true;

  if (options) {
    if (typeof options.read === 'function') this._read = options.read;

    if (typeof options.destroy === 'function') this._destroy = options.destroy;
  }

  Stream.call(this);
}

Object.defineProperty(Readable.prototype, 'destroyed', {
  get: function () {
    if (this._readableState === undefined) {
      return false;
    }
    return this._readableState.destroyed;
  },
  set: function (value) {
    // we ignore the value if the stream
    // has not been initialized yet
    if (!this._readableState) {
      return;
    }

    // backward compatibility, the user is explicitly
    // managing destroyed
    this._readableState.destroyed = value;
  }
});

Readable.prototype.destroy = destroyImpl.destroy;
Readable.prototype._undestroy = destroyImpl.undestroy;
Readable.prototype._destroy = function (err, cb) {
  this.push(null);
  cb(err);
};

// Manually shove something into the read() buffer.
// This returns true if the highWaterMark has not been hit yet,
// similar to how Writable.write() returns true if you should
// write() some more.
Readable.prototype.push = function (chunk, encoding) {
  var state = this._readableState;
  var skipChunkCheck;

  if (!state.objectMode) {
    if (typeof chunk === 'string') {
      encoding = encoding || state.defaultEncoding;
      if (encoding !== state.encoding) {
        chunk = Buffer.from(chunk, encoding);
        encoding = '';
      }
      skipChunkCheck = true;
    }
  } else {
    skipChunkCheck = true;
  }

  return readableAddChunk(this, chunk, encoding, false, skipChunkCheck);
};

// Unshift should *always* be something directly out of read()
Readable.prototype.unshift = function (chunk) {
  return readableAddChunk(this, chunk, null, true, false);
};

function readableAddChunk(stream, chunk, encoding, addToFront, skipChunkCheck) {
  var state = stream._readableState;
  if (chunk === null) {
    state.reading = false;
    onEofChunk(stream, state);
  } else {
    var er;
    if (!skipChunkCheck) er = chunkInvalid(state, chunk);
    if (er) {
      stream.emit('error', er);
    } else if (state.objectMode || chunk && chunk.length > 0) {
      if (typeof chunk !== 'string' && !state.objectMode && Object.getPrototypeOf(chunk) !== Buffer.prototype) {
        chunk = _uint8ArrayToBuffer(chunk);
      }

      if (addToFront) {
        if (state.endEmitted) stream.emit('error', new Error('stream.unshift() after end event'));else addChunk(stream, state, chunk, true);
      } else if (state.ended) {
        stream.emit('error', new Error('stream.push() after EOF'));
      } else {
        state.reading = false;
        if (state.decoder && !encoding) {
          chunk = state.decoder.write(chunk);
          if (state.objectMode || chunk.length !== 0) addChunk(stream, state, chunk, false);else maybeReadMore(stream, state);
        } else {
          addChunk(stream, state, chunk, false);
        }
      }
    } else if (!addToFront) {
      state.reading = false;
    }
  }

  return needMoreData(state);
}

function addChunk(stream, state, chunk, addToFront) {
  if (state.flowing && state.length === 0 && !state.sync) {
    stream.emit('data', chunk);
    stream.read(0);
  } else {
    // update the buffer info.
    state.length += state.objectMode ? 1 : chunk.length;
    if (addToFront) state.buffer.unshift(chunk);else state.buffer.push(chunk);

    if (state.needReadable) emitReadable(stream);
  }
  maybeReadMore(stream, state);
}

function chunkInvalid(state, chunk) {
  var er;
  if (!_isUint8Array(chunk) && typeof chunk !== 'string' && chunk !== undefined && !state.objectMode) {
    er = new TypeError('Invalid non-string/buffer chunk');
  }
  return er;
}

// if it's past the high water mark, we can push in some more.
// Also, if we have no data yet, we can stand some
// more bytes.  This is to work around cases where hwm=0,
// such as the repl.  Also, if the push() triggered a
// readable event, and the user called read(largeNumber) such that
// needReadable was set, then we ought to push more, so that another
// 'readable' event will be triggered.
function needMoreData(state) {
  return !state.ended && (state.needReadable || state.length < state.highWaterMark || state.length === 0);
}

Readable.prototype.isPaused = function () {
  return this._readableState.flowing === false;
};

// backwards compatibility.
Readable.prototype.setEncoding = function (enc) {
  if (!StringDecoder) StringDecoder = __webpack_require__(125).StringDecoder;
  this._readableState.decoder = new StringDecoder(enc);
  this._readableState.encoding = enc;
  return this;
};

// Don't raise the hwm > 8MB
var MAX_HWM = 0x800000;
function computeNewHighWaterMark(n) {
  if (n >= MAX_HWM) {
    n = MAX_HWM;
  } else {
    // Get the next highest power of 2 to prevent increasing hwm excessively in
    // tiny amounts
    n--;
    n |= n >>> 1;
    n |= n >>> 2;
    n |= n >>> 4;
    n |= n >>> 8;
    n |= n >>> 16;
    n++;
  }
  return n;
}

// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function howMuchToRead(n, state) {
  if (n <= 0 || state.length === 0 && state.ended) return 0;
  if (state.objectMode) return 1;
  if (n !== n) {
    // Only flow one buffer at a time
    if (state.flowing && state.length) return state.buffer.head.data.length;else return state.length;
  }
  // If we're asking for more than the current hwm, then raise the hwm.
  if (n > state.highWaterMark) state.highWaterMark = computeNewHighWaterMark(n);
  if (n <= state.length) return n;
  // Don't have enough
  if (!state.ended) {
    state.needReadable = true;
    return 0;
  }
  return state.length;
}

// you can override either this method, or the async _read(n) below.
Readable.prototype.read = function (n) {
  debug('read', n);
  n = parseInt(n, 10);
  var state = this._readableState;
  var nOrig = n;

  if (n !== 0) state.emittedReadable = false;

  // if we're doing read(0) to trigger a readable event, but we
  // already have a bunch of data in the buffer, then just trigger
  // the 'readable' event and move on.
  if (n === 0 && state.needReadable && (state.length >= state.highWaterMark || state.ended)) {
    debug('read: emitReadable', state.length, state.ended);
    if (state.length === 0 && state.ended) endReadable(this);else emitReadable(this);
    return null;
  }

  n = howMuchToRead(n, state);

  // if we've ended, and we're now clear, then finish it up.
  if (n === 0 && state.ended) {
    if (state.length === 0) endReadable(this);
    return null;
  }

  // All the actual chunk generation logic needs to be
  // *below* the call to _read.  The reason is that in certain
  // synthetic stream cases, such as passthrough streams, _read
  // may be a completely synchronous operation which may change
  // the state of the read buffer, providing enough data when
  // before there was *not* enough.
  //
  // So, the steps are:
  // 1. Figure out what the state of things will be after we do
  // a read from the buffer.
  //
  // 2. If that resulting state will trigger a _read, then call _read.
  // Note that this may be asynchronous, or synchronous.  Yes, it is
  // deeply ugly to write APIs this way, but that still doesn't mean
  // that the Readable class should behave improperly, as streams are
  // designed to be sync/async agnostic.
  // Take note if the _read call is sync or async (ie, if the read call
  // has returned yet), so that we know whether or not it's safe to emit
  // 'readable' etc.
  //
  // 3. Actually pull the requested chunks out of the buffer and return.

  // if we need a readable event, then we need to do some reading.
  var doRead = state.needReadable;
  debug('need readable', doRead);

  // if we currently have less than the highWaterMark, then also read some
  if (state.length === 0 || state.length - n < state.highWaterMark) {
    doRead = true;
    debug('length less than watermark', doRead);
  }

  // however, if we've ended, then there's no point, and if we're already
  // reading, then it's unnecessary.
  if (state.ended || state.reading) {
    doRead = false;
    debug('reading or ended', doRead);
  } else if (doRead) {
    debug('do read');
    state.reading = true;
    state.sync = true;
    // if the length is currently zero, then we *need* a readable event.
    if (state.length === 0) state.needReadable = true;
    // call internal read method
    this._read(state.highWaterMark);
    state.sync = false;
    // If _read pushed data synchronously, then `reading` will be false,
    // and we need to re-evaluate how much data we can return to the user.
    if (!state.reading) n = howMuchToRead(nOrig, state);
  }

  var ret;
  if (n > 0) ret = fromList(n, state);else ret = null;

  if (ret === null) {
    state.needReadable = true;
    n = 0;
  } else {
    state.length -= n;
  }

  if (state.length === 0) {
    // If we have nothing in the buffer, then we want to know
    // as soon as we *do* get something into the buffer.
    if (!state.ended) state.needReadable = true;

    // If we tried to read() past the EOF, then emit end on the next tick.
    if (nOrig !== n && state.ended) endReadable(this);
  }

  if (ret !== null) this.emit('data', ret);

  return ret;
};

function onEofChunk(stream, state) {
  if (state.ended) return;
  if (state.decoder) {
    var chunk = state.decoder.end();
    if (chunk && chunk.length) {
      state.buffer.push(chunk);
      state.length += state.objectMode ? 1 : chunk.length;
    }
  }
  state.ended = true;

  // emit 'readable' now to make sure it gets picked up.
  emitReadable(stream);
}

// Don't emit readable right away in sync mode, because this can trigger
// another read() call => stack overflow.  This way, it might trigger
// a nextTick recursion warning, but that's not so bad.
function emitReadable(stream) {
  var state = stream._readableState;
  state.needReadable = false;
  if (!state.emittedReadable) {
    debug('emitReadable', state.flowing);
    state.emittedReadable = true;
    if (state.sync) pna.nextTick(emitReadable_, stream);else emitReadable_(stream);
  }
}

function emitReadable_(stream) {
  debug('emit readable');
  stream.emit('readable');
  flow(stream);
}

// at this point, the user has presumably seen the 'readable' event,
// and called read() to consume some data.  that may have triggered
// in turn another _read(n) call, in which case reading = true if
// it's in progress.
// However, if we're not ended, or reading, and the length < hwm,
// then go ahead and try to read some more preemptively.
function maybeReadMore(stream, state) {
  if (!state.readingMore) {
    state.readingMore = true;
    pna.nextTick(maybeReadMore_, stream, state);
  }
}

function maybeReadMore_(stream, state) {
  var len = state.length;
  while (!state.reading && !state.flowing && !state.ended && state.length < state.highWaterMark) {
    debug('maybeReadMore read 0');
    stream.read(0);
    if (len === state.length)
      // didn't get any data, stop spinning.
      break;else len = state.length;
  }
  state.readingMore = false;
}

// abstract method.  to be overridden in specific implementation classes.
// call cb(er, data) where data is <= n in length.
// for virtual (non-string, non-buffer) streams, "length" is somewhat
// arbitrary, and perhaps not very meaningful.
Readable.prototype._read = function (n) {
  this.emit('error', new Error('_read() is not implemented'));
};

Readable.prototype.pipe = function (dest, pipeOpts) {
  var src = this;
  var state = this._readableState;

  switch (state.pipesCount) {
    case 0:
      state.pipes = dest;
      break;
    case 1:
      state.pipes = [state.pipes, dest];
      break;
    default:
      state.pipes.push(dest);
      break;
  }
  state.pipesCount += 1;
  debug('pipe count=%d opts=%j', state.pipesCount, pipeOpts);

  var doEnd = (!pipeOpts || pipeOpts.end !== false) && dest !== process.stdout && dest !== process.stderr;

  var endFn = doEnd ? onend : unpipe;
  if (state.endEmitted) pna.nextTick(endFn);else src.once('end', endFn);

  dest.on('unpipe', onunpipe);
  function onunpipe(readable, unpipeInfo) {
    debug('onunpipe');
    if (readable === src) {
      if (unpipeInfo && unpipeInfo.hasUnpiped === false) {
        unpipeInfo.hasUnpiped = true;
        cleanup();
      }
    }
  }

  function onend() {
    debug('onend');
    dest.end();
  }

  // when the dest drains, it reduces the awaitDrain counter
  // on the source.  This would be more elegant with a .once()
  // handler in flow(), but adding and removing repeatedly is
  // too slow.
  var ondrain = pipeOnDrain(src);
  dest.on('drain', ondrain);

  var cleanedUp = false;
  function cleanup() {
    debug('cleanup');
    // cleanup event handlers once the pipe is broken
    dest.removeListener('close', onclose);
    dest.removeListener('finish', onfinish);
    dest.removeListener('drain', ondrain);
    dest.removeListener('error', onerror);
    dest.removeListener('unpipe', onunpipe);
    src.removeListener('end', onend);
    src.removeListener('end', unpipe);
    src.removeListener('data', ondata);

    cleanedUp = true;

    // if the reader is waiting for a drain event from this
    // specific writer, then it would cause it to never start
    // flowing again.
    // So, if this is awaiting a drain, then we just call it now.
    // If we don't know, then assume that we are waiting for one.
    if (state.awaitDrain && (!dest._writableState || dest._writableState.needDrain)) ondrain();
  }

  // If the user pushes more data while we're writing to dest then we'll end up
  // in ondata again. However, we only want to increase awaitDrain once because
  // dest will only emit one 'drain' event for the multiple writes.
  // => Introduce a guard on increasing awaitDrain.
  var increasedAwaitDrain = false;
  src.on('data', ondata);
  function ondata(chunk) {
    debug('ondata');
    increasedAwaitDrain = false;
    var ret = dest.write(chunk);
    if (false === ret && !increasedAwaitDrain) {
      // If the user unpiped during `dest.write()`, it is possible
      // to get stuck in a permanently paused state if that write
      // also returned false.
      // => Check whether `dest` is still a piping destination.
      if ((state.pipesCount === 1 && state.pipes === dest || state.pipesCount > 1 && indexOf(state.pipes, dest) !== -1) && !cleanedUp) {
        debug('false write response, pause', src._readableState.awaitDrain);
        src._readableState.awaitDrain++;
        increasedAwaitDrain = true;
      }
      src.pause();
    }
  }

  // if the dest has an error, then stop piping into it.
  // however, don't suppress the throwing behavior for this.
  function onerror(er) {
    debug('onerror', er);
    unpipe();
    dest.removeListener('error', onerror);
    if (EElistenerCount(dest, 'error') === 0) dest.emit('error', er);
  }

  // Make sure our error handler is attached before userland ones.
  prependListener(dest, 'error', onerror);

  // Both close and finish should trigger unpipe, but only once.
  function onclose() {
    dest.removeListener('finish', onfinish);
    unpipe();
  }
  dest.once('close', onclose);
  function onfinish() {
    debug('onfinish');
    dest.removeListener('close', onclose);
    unpipe();
  }
  dest.once('finish', onfinish);

  function unpipe() {
    debug('unpipe');
    src.unpipe(dest);
  }

  // tell the dest that it's being piped to
  dest.emit('pipe', src);

  // start the flow if it hasn't been started already.
  if (!state.flowing) {
    debug('pipe resume');
    src.resume();
  }

  return dest;
};

function pipeOnDrain(src) {
  return function () {
    var state = src._readableState;
    debug('pipeOnDrain', state.awaitDrain);
    if (state.awaitDrain) state.awaitDrain--;
    if (state.awaitDrain === 0 && EElistenerCount(src, 'data')) {
      state.flowing = true;
      flow(src);
    }
  };
}

Readable.prototype.unpipe = function (dest) {
  var state = this._readableState;
  var unpipeInfo = { hasUnpiped: false };

  // if we're not piping anywhere, then do nothing.
  if (state.pipesCount === 0) return this;

  // just one destination.  most common case.
  if (state.pipesCount === 1) {
    // passed in one, but it's not the right one.
    if (dest && dest !== state.pipes) return this;

    if (!dest) dest = state.pipes;

    // got a match.
    state.pipes = null;
    state.pipesCount = 0;
    state.flowing = false;
    if (dest) dest.emit('unpipe', this, unpipeInfo);
    return this;
  }

  // slow case. multiple pipe destinations.

  if (!dest) {
    // remove all.
    var dests = state.pipes;
    var len = state.pipesCount;
    state.pipes = null;
    state.pipesCount = 0;
    state.flowing = false;

    for (var i = 0; i < len; i++) {
      dests[i].emit('unpipe', this, unpipeInfo);
    }return this;
  }

  // try to find the right one.
  var index = indexOf(state.pipes, dest);
  if (index === -1) return this;

  state.pipes.splice(index, 1);
  state.pipesCount -= 1;
  if (state.pipesCount === 1) state.pipes = state.pipes[0];

  dest.emit('unpipe', this, unpipeInfo);

  return this;
};

// set up data events if they are asked for
// Ensure readable listeners eventually get something
Readable.prototype.on = function (ev, fn) {
  var res = Stream.prototype.on.call(this, ev, fn);

  if (ev === 'data') {
    // Start flowing on next tick if stream isn't explicitly paused
    if (this._readableState.flowing !== false) this.resume();
  } else if (ev === 'readable') {
    var state = this._readableState;
    if (!state.endEmitted && !state.readableListening) {
      state.readableListening = state.needReadable = true;
      state.emittedReadable = false;
      if (!state.reading) {
        pna.nextTick(nReadingNextTick, this);
      } else if (state.length) {
        emitReadable(this);
      }
    }
  }

  return res;
};
Readable.prototype.addListener = Readable.prototype.on;

function nReadingNextTick(self) {
  debug('readable nexttick read 0');
  self.read(0);
}

// pause() and resume() are remnants of the legacy readable stream API
// If the user uses them, then switch into old mode.
Readable.prototype.resume = function () {
  var state = this._readableState;
  if (!state.flowing) {
    debug('resume');
    state.flowing = true;
    resume(this, state);
  }
  return this;
};

function resume(stream, state) {
  if (!state.resumeScheduled) {
    state.resumeScheduled = true;
    pna.nextTick(resume_, stream, state);
  }
}

function resume_(stream, state) {
  if (!state.reading) {
    debug('resume read 0');
    stream.read(0);
  }

  state.resumeScheduled = false;
  state.awaitDrain = 0;
  stream.emit('resume');
  flow(stream);
  if (state.flowing && !state.reading) stream.read(0);
}

Readable.prototype.pause = function () {
  debug('call pause flowing=%j', this._readableState.flowing);
  if (false !== this._readableState.flowing) {
    debug('pause');
    this._readableState.flowing = false;
    this.emit('pause');
  }
  return this;
};

function flow(stream) {
  var state = stream._readableState;
  debug('flow', state.flowing);
  while (state.flowing && stream.read() !== null) {}
}

// wrap an old-style stream as the async data source.
// This is *not* part of the readable stream interface.
// It is an ugly unfortunate mess of history.
Readable.prototype.wrap = function (stream) {
  var _this = this;

  var state = this._readableState;
  var paused = false;

  stream.on('end', function () {
    debug('wrapped end');
    if (state.decoder && !state.ended) {
      var chunk = state.decoder.end();
      if (chunk && chunk.length) _this.push(chunk);
    }

    _this.push(null);
  });

  stream.on('data', function (chunk) {
    debug('wrapped data');
    if (state.decoder) chunk = state.decoder.write(chunk);

    // don't skip over falsy values in objectMode
    if (state.objectMode && (chunk === null || chunk === undefined)) return;else if (!state.objectMode && (!chunk || !chunk.length)) return;

    var ret = _this.push(chunk);
    if (!ret) {
      paused = true;
      stream.pause();
    }
  });

  // proxy all the other methods.
  // important when wrapping filters and duplexes.
  for (var i in stream) {
    if (this[i] === undefined && typeof stream[i] === 'function') {
      this[i] = function (method) {
        return function () {
          return stream[method].apply(stream, arguments);
        };
      }(i);
    }
  }

  // proxy certain important events.
  for (var n = 0; n < kProxyEvents.length; n++) {
    stream.on(kProxyEvents[n], this.emit.bind(this, kProxyEvents[n]));
  }

  // when we try to consume some more bytes, simply unpause the
  // underlying stream.
  this._read = function (n) {
    debug('wrapped _read', n);
    if (paused) {
      paused = false;
      stream.resume();
    }
  };

  return this;
};

Object.defineProperty(Readable.prototype, 'readableHighWaterMark', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function () {
    return this._readableState.highWaterMark;
  }
});

// exposed for testing purposes only.
Readable._fromList = fromList;

// Pluck off n bytes from an array of buffers.
// Length is the combined lengths of all the buffers in the list.
// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function fromList(n, state) {
  // nothing buffered
  if (state.length === 0) return null;

  var ret;
  if (state.objectMode) ret = state.buffer.shift();else if (!n || n >= state.length) {
    // read it all, truncate the list
    if (state.decoder) ret = state.buffer.join('');else if (state.buffer.length === 1) ret = state.buffer.head.data;else ret = state.buffer.concat(state.length);
    state.buffer.clear();
  } else {
    // read part of list
    ret = fromListPartial(n, state.buffer, state.decoder);
  }

  return ret;
}

// Extracts only enough buffered data to satisfy the amount requested.
// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function fromListPartial(n, list, hasStrings) {
  var ret;
  if (n < list.head.data.length) {
    // slice is the same for buffers and strings
    ret = list.head.data.slice(0, n);
    list.head.data = list.head.data.slice(n);
  } else if (n === list.head.data.length) {
    // first chunk is a perfect match
    ret = list.shift();
  } else {
    // result spans more than one buffer
    ret = hasStrings ? copyFromBufferString(n, list) : copyFromBuffer(n, list);
  }
  return ret;
}

// Copies a specified amount of characters from the list of buffered data
// chunks.
// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function copyFromBufferString(n, list) {
  var p = list.head;
  var c = 1;
  var ret = p.data;
  n -= ret.length;
  while (p = p.next) {
    var str = p.data;
    var nb = n > str.length ? str.length : n;
    if (nb === str.length) ret += str;else ret += str.slice(0, n);
    n -= nb;
    if (n === 0) {
      if (nb === str.length) {
        ++c;
        if (p.next) list.head = p.next;else list.head = list.tail = null;
      } else {
        list.head = p;
        p.data = str.slice(nb);
      }
      break;
    }
    ++c;
  }
  list.length -= c;
  return ret;
}

// Copies a specified amount of bytes from the list of buffered data chunks.
// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function copyFromBuffer(n, list) {
  var ret = Buffer.allocUnsafe(n);
  var p = list.head;
  var c = 1;
  p.data.copy(ret);
  n -= p.data.length;
  while (p = p.next) {
    var buf = p.data;
    var nb = n > buf.length ? buf.length : n;
    buf.copy(ret, ret.length - n, 0, nb);
    n -= nb;
    if (n === 0) {
      if (nb === buf.length) {
        ++c;
        if (p.next) list.head = p.next;else list.head = list.tail = null;
      } else {
        list.head = p;
        p.data = buf.slice(nb);
      }
      break;
    }
    ++c;
  }
  list.length -= c;
  return ret;
}

function endReadable(stream) {
  var state = stream._readableState;

  // If we get here before consuming all the bytes, then that is a
  // bug in node.  Should never happen.
  if (state.length > 0) throw new Error('"endReadable()" called on non-empty stream');

  if (!state.endEmitted) {
    state.ended = true;
    pna.nextTick(endReadableNT, state, stream);
  }
}

function endReadableNT(state, stream) {
  // Check that we didn't get one last unshift.
  if (!state.endEmitted && state.length === 0) {
    state.endEmitted = true;
    stream.readable = false;
    stream.emit('end');
  }
}

function indexOf(xs, x) {
  for (var i = 0, l = xs.length; i < l; i++) {
    if (xs[i] === x) return i;
  }
  return -1;
}
/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(24), __webpack_require__(47)))

/***/ }),
/* 241 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = __webpack_require__(122).EventEmitter;


/***/ }),
/* 242 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


/*<replacement>*/

var pna = __webpack_require__(123);
/*</replacement>*/

// undocumented cb() API, needed for core, not for public API
function destroy(err, cb) {
  var _this = this;

  var readableDestroyed = this._readableState && this._readableState.destroyed;
  var writableDestroyed = this._writableState && this._writableState.destroyed;

  if (readableDestroyed || writableDestroyed) {
    if (cb) {
      cb(err);
    } else if (err && (!this._writableState || !this._writableState.errorEmitted)) {
      pna.nextTick(emitErrorNT, this, err);
    }
    return this;
  }

  // we set destroyed to true before firing error callbacks in order
  // to make it re-entrance safe in case destroy() is called within callbacks

  if (this._readableState) {
    this._readableState.destroyed = true;
  }

  // if this is a duplex stream mark the writable part as destroyed as well
  if (this._writableState) {
    this._writableState.destroyed = true;
  }

  this._destroy(err || null, function (err) {
    if (!cb && err) {
      pna.nextTick(emitErrorNT, _this, err);
      if (_this._writableState) {
        _this._writableState.errorEmitted = true;
      }
    } else if (cb) {
      cb(err);
    }
  });

  return this;
}

function undestroy() {
  if (this._readableState) {
    this._readableState.destroyed = false;
    this._readableState.reading = false;
    this._readableState.ended = false;
    this._readableState.endEmitted = false;
  }

  if (this._writableState) {
    this._writableState.destroyed = false;
    this._writableState.ended = false;
    this._writableState.ending = false;
    this._writableState.finished = false;
    this._writableState.errorEmitted = false;
  }
}

function emitErrorNT(self, err) {
  self.emit('error', err);
}

module.exports = {
  destroy: destroy,
  undestroy: undestroy
};

/***/ }),
/* 243 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

// a transform stream is a readable/writable stream where you do
// something with the data.  Sometimes it's called a "filter",
// but that's not a great name for it, since that implies a thing where
// some bits pass through, and others are simply ignored.  (That would
// be a valid example of a transform, of course.)
//
// While the output is causally related to the input, it's not a
// necessarily symmetric or synchronous transformation.  For example,
// a zlib stream might take multiple plain-text writes(), and then
// emit a single compressed chunk some time in the future.
//
// Here's how this works:
//
// The Transform stream has all the aspects of the readable and writable
// stream classes.  When you write(chunk), that calls _write(chunk,cb)
// internally, and returns false if there's a lot of pending writes
// buffered up.  When you call read(), that calls _read(n) until
// there's enough pending readable data buffered up.
//
// In a transform stream, the written data is placed in a buffer.  When
// _read(n) is called, it transforms the queued up data, calling the
// buffered _write cb's as it consumes chunks.  If consuming a single
// written chunk would result in multiple output chunks, then the first
// outputted bit calls the readcb, and subsequent chunks just go into
// the read buffer, and will cause it to emit 'readable' if necessary.
//
// This way, back-pressure is actually determined by the reading side,
// since _read has to be called to start processing a new chunk.  However,
// a pathological inflate type of transform can cause excessive buffering
// here.  For example, imagine a stream where every byte of input is
// interpreted as an integer from 0-255, and then results in that many
// bytes of output.  Writing the 4 bytes {ff,ff,ff,ff} would result in
// 1kb of data being output.  In this case, you could write a very small
// amount of input, and end up with a very large amount of output.  In
// such a pathological inflating mechanism, there'd be no way to tell
// the system to stop doing the transform.  A single 4MB write could
// cause the system to run out of memory.
//
// However, even in such a pathological case, only a single written chunk
// would be consumed, and then the rest would wait (un-transformed) until
// the results of the previous transformed chunk were consumed.



module.exports = Transform;

var Duplex = __webpack_require__(48);

/*<replacement>*/
var util = Object.create(__webpack_require__(98));
util.inherits = __webpack_require__(86);
/*</replacement>*/

util.inherits(Transform, Duplex);

function afterTransform(er, data) {
  var ts = this._transformState;
  ts.transforming = false;

  var cb = ts.writecb;

  if (!cb) {
    return this.emit('error', new Error('write callback called multiple times'));
  }

  ts.writechunk = null;
  ts.writecb = null;

  if (data != null) // single equals check for both `null` and `undefined`
    this.push(data);

  cb(er);

  var rs = this._readableState;
  rs.reading = false;
  if (rs.needReadable || rs.length < rs.highWaterMark) {
    this._read(rs.highWaterMark);
  }
}

function Transform(options) {
  if (!(this instanceof Transform)) return new Transform(options);

  Duplex.call(this, options);

  this._transformState = {
    afterTransform: afterTransform.bind(this),
    needTransform: false,
    transforming: false,
    writecb: null,
    writechunk: null,
    writeencoding: null
  };

  // start out asking for a readable event once data is transformed.
  this._readableState.needReadable = true;

  // we have implemented the _read method, and done the other things
  // that Readable wants before the first _read call, so unset the
  // sync guard flag.
  this._readableState.sync = false;

  if (options) {
    if (typeof options.transform === 'function') this._transform = options.transform;

    if (typeof options.flush === 'function') this._flush = options.flush;
  }

  // When the writable side finishes, then flush out anything remaining.
  this.on('prefinish', prefinish);
}

function prefinish() {
  var _this = this;

  if (typeof this._flush === 'function') {
    this._flush(function (er, data) {
      done(_this, er, data);
    });
  } else {
    done(this, null, null);
  }
}

Transform.prototype.push = function (chunk, encoding) {
  this._transformState.needTransform = false;
  return Duplex.prototype.push.call(this, chunk, encoding);
};

// This is the part where you do stuff!
// override this function in implementation classes.
// 'chunk' is an input chunk.
//
// Call `push(newChunk)` to pass along transformed output
// to the readable side.  You may call 'push' zero or more times.
//
// Call `cb(err)` when you are done with this chunk.  If you pass
// an error, then that'll put the hurt on the whole operation.  If you
// never call cb(), then you'll never get another chunk.
Transform.prototype._transform = function (chunk, encoding, cb) {
  throw new Error('_transform() is not implemented');
};

Transform.prototype._write = function (chunk, encoding, cb) {
  var ts = this._transformState;
  ts.writecb = cb;
  ts.writechunk = chunk;
  ts.writeencoding = encoding;
  if (!ts.transforming) {
    var rs = this._readableState;
    if (ts.needTransform || rs.needReadable || rs.length < rs.highWaterMark) this._read(rs.highWaterMark);
  }
};

// Doesn't matter what the args are here.
// _transform does all the work.
// That we got here means that the readable side wants more data.
Transform.prototype._read = function (n) {
  var ts = this._transformState;

  if (ts.writechunk !== null && ts.writecb && !ts.transforming) {
    ts.transforming = true;
    this._transform(ts.writechunk, ts.writeencoding, ts.afterTransform);
  } else {
    // mark that we need a transform, so that any data that comes in
    // will get processed, now that we've asked for it.
    ts.needTransform = true;
  }
};

Transform.prototype._destroy = function (err, cb) {
  var _this2 = this;

  Duplex.prototype._destroy.call(this, err, function (err2) {
    cb(err2);
    _this2.emit('close');
  });
};

function done(stream, er, data) {
  if (er) return stream.emit('error', er);

  if (data != null) // single equals check for both `null` and `undefined`
    stream.push(data);

  // if there's nothing in the write buffer, then that means
  // that nothing more will ever be provided
  if (stream._writableState.length) throw new Error('Calling transform done when ws.length != 0');

  if (stream._transformState.transforming) throw new Error('Calling transform done when still transforming');

  return stream.push(null);
}

/***/ }),
/* 244 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(process) {

var Buffer = __webpack_require__(10).Buffer;
var Transform = __webpack_require__(121).Transform;
var binding = __webpack_require__(350);
var util = __webpack_require__(166);
var assert = __webpack_require__(245).ok;
var kMaxLength = __webpack_require__(10).kMaxLength;
var kRangeErrorMessage = 'Cannot create final Buffer. It would be larger ' + 'than 0x' + kMaxLength.toString(16) + ' bytes';

// zlib doesn't provide these, so kludge them in following the same
// const naming scheme zlib uses.
binding.Z_MIN_WINDOWBITS = 8;
binding.Z_MAX_WINDOWBITS = 15;
binding.Z_DEFAULT_WINDOWBITS = 15;

// fewer than 64 bytes per chunk is stupid.
// technically it could work with as few as 8, but even 64 bytes
// is absurdly low.  Usually a MB or more is best.
binding.Z_MIN_CHUNK = 64;
binding.Z_MAX_CHUNK = Infinity;
binding.Z_DEFAULT_CHUNK = 16 * 1024;

binding.Z_MIN_MEMLEVEL = 1;
binding.Z_MAX_MEMLEVEL = 9;
binding.Z_DEFAULT_MEMLEVEL = 8;

binding.Z_MIN_LEVEL = -1;
binding.Z_MAX_LEVEL = 9;
binding.Z_DEFAULT_LEVEL = binding.Z_DEFAULT_COMPRESSION;

// expose all the zlib constants
var bkeys = Object.keys(binding);
for (var bk = 0; bk < bkeys.length; bk++) {
  var bkey = bkeys[bk];
  if (bkey.match(/^Z/)) {
    Object.defineProperty(exports, bkey, {
      enumerable: true, value: binding[bkey], writable: false
    });
  }
}

// translation table for return codes.
var codes = {
  Z_OK: binding.Z_OK,
  Z_STREAM_END: binding.Z_STREAM_END,
  Z_NEED_DICT: binding.Z_NEED_DICT,
  Z_ERRNO: binding.Z_ERRNO,
  Z_STREAM_ERROR: binding.Z_STREAM_ERROR,
  Z_DATA_ERROR: binding.Z_DATA_ERROR,
  Z_MEM_ERROR: binding.Z_MEM_ERROR,
  Z_BUF_ERROR: binding.Z_BUF_ERROR,
  Z_VERSION_ERROR: binding.Z_VERSION_ERROR
};

var ckeys = Object.keys(codes);
for (var ck = 0; ck < ckeys.length; ck++) {
  var ckey = ckeys[ck];
  codes[codes[ckey]] = ckey;
}

Object.defineProperty(exports, 'codes', {
  enumerable: true, value: Object.freeze(codes), writable: false
});

exports.Deflate = Deflate;
exports.Inflate = Inflate;
exports.Gzip = Gzip;
exports.Gunzip = Gunzip;
exports.DeflateRaw = DeflateRaw;
exports.InflateRaw = InflateRaw;
exports.Unzip = Unzip;

exports.createDeflate = function (o) {
  return new Deflate(o);
};

exports.createInflate = function (o) {
  return new Inflate(o);
};

exports.createDeflateRaw = function (o) {
  return new DeflateRaw(o);
};

exports.createInflateRaw = function (o) {
  return new InflateRaw(o);
};

exports.createGzip = function (o) {
  return new Gzip(o);
};

exports.createGunzip = function (o) {
  return new Gunzip(o);
};

exports.createUnzip = function (o) {
  return new Unzip(o);
};

// Convenience methods.
// compress/decompress a string or buffer in one step.
exports.deflate = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new Deflate(opts), buffer, callback);
};

exports.deflateSync = function (buffer, opts) {
  return zlibBufferSync(new Deflate(opts), buffer);
};

exports.gzip = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new Gzip(opts), buffer, callback);
};

exports.gzipSync = function (buffer, opts) {
  return zlibBufferSync(new Gzip(opts), buffer);
};

exports.deflateRaw = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new DeflateRaw(opts), buffer, callback);
};

exports.deflateRawSync = function (buffer, opts) {
  return zlibBufferSync(new DeflateRaw(opts), buffer);
};

exports.unzip = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new Unzip(opts), buffer, callback);
};

exports.unzipSync = function (buffer, opts) {
  return zlibBufferSync(new Unzip(opts), buffer);
};

exports.inflate = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new Inflate(opts), buffer, callback);
};

exports.inflateSync = function (buffer, opts) {
  return zlibBufferSync(new Inflate(opts), buffer);
};

exports.gunzip = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new Gunzip(opts), buffer, callback);
};

exports.gunzipSync = function (buffer, opts) {
  return zlibBufferSync(new Gunzip(opts), buffer);
};

exports.inflateRaw = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new InflateRaw(opts), buffer, callback);
};

exports.inflateRawSync = function (buffer, opts) {
  return zlibBufferSync(new InflateRaw(opts), buffer);
};

function zlibBuffer(engine, buffer, callback) {
  var buffers = [];
  var nread = 0;

  engine.on('error', onError);
  engine.on('end', onEnd);

  engine.end(buffer);
  flow();

  function flow() {
    var chunk;
    while (null !== (chunk = engine.read())) {
      buffers.push(chunk);
      nread += chunk.length;
    }
    engine.once('readable', flow);
  }

  function onError(err) {
    engine.removeListener('end', onEnd);
    engine.removeListener('readable', flow);
    callback(err);
  }

  function onEnd() {
    var buf;
    var err = null;

    if (nread >= kMaxLength) {
      err = new RangeError(kRangeErrorMessage);
    } else {
      buf = Buffer.concat(buffers, nread);
    }

    buffers = [];
    engine.close();
    callback(err, buf);
  }
}

function zlibBufferSync(engine, buffer) {
  if (typeof buffer === 'string') buffer = Buffer.from(buffer);

  if (!Buffer.isBuffer(buffer)) throw new TypeError('Not a string or buffer');

  var flushFlag = engine._finishFlushFlag;

  return engine._processChunk(buffer, flushFlag);
}

// generic zlib
// minimal 2-byte header
function Deflate(opts) {
  if (!(this instanceof Deflate)) return new Deflate(opts);
  Zlib.call(this, opts, binding.DEFLATE);
}

function Inflate(opts) {
  if (!(this instanceof Inflate)) return new Inflate(opts);
  Zlib.call(this, opts, binding.INFLATE);
}

// gzip - bigger header, same deflate compression
function Gzip(opts) {
  if (!(this instanceof Gzip)) return new Gzip(opts);
  Zlib.call(this, opts, binding.GZIP);
}

function Gunzip(opts) {
  if (!(this instanceof Gunzip)) return new Gunzip(opts);
  Zlib.call(this, opts, binding.GUNZIP);
}

// raw - no header
function DeflateRaw(opts) {
  if (!(this instanceof DeflateRaw)) return new DeflateRaw(opts);
  Zlib.call(this, opts, binding.DEFLATERAW);
}

function InflateRaw(opts) {
  if (!(this instanceof InflateRaw)) return new InflateRaw(opts);
  Zlib.call(this, opts, binding.INFLATERAW);
}

// auto-detect header.
function Unzip(opts) {
  if (!(this instanceof Unzip)) return new Unzip(opts);
  Zlib.call(this, opts, binding.UNZIP);
}

function isValidFlushFlag(flag) {
  return flag === binding.Z_NO_FLUSH || flag === binding.Z_PARTIAL_FLUSH || flag === binding.Z_SYNC_FLUSH || flag === binding.Z_FULL_FLUSH || flag === binding.Z_FINISH || flag === binding.Z_BLOCK;
}

// the Zlib class they all inherit from
// This thing manages the queue of requests, and returns
// true or false if there is anything in the queue when
// you call the .write() method.

function Zlib(opts, mode) {
  var _this = this;

  this._opts = opts = opts || {};
  this._chunkSize = opts.chunkSize || exports.Z_DEFAULT_CHUNK;

  Transform.call(this, opts);

  if (opts.flush && !isValidFlushFlag(opts.flush)) {
    throw new Error('Invalid flush flag: ' + opts.flush);
  }
  if (opts.finishFlush && !isValidFlushFlag(opts.finishFlush)) {
    throw new Error('Invalid flush flag: ' + opts.finishFlush);
  }

  this._flushFlag = opts.flush || binding.Z_NO_FLUSH;
  this._finishFlushFlag = typeof opts.finishFlush !== 'undefined' ? opts.finishFlush : binding.Z_FINISH;

  if (opts.chunkSize) {
    if (opts.chunkSize < exports.Z_MIN_CHUNK || opts.chunkSize > exports.Z_MAX_CHUNK) {
      throw new Error('Invalid chunk size: ' + opts.chunkSize);
    }
  }

  if (opts.windowBits) {
    if (opts.windowBits < exports.Z_MIN_WINDOWBITS || opts.windowBits > exports.Z_MAX_WINDOWBITS) {
      throw new Error('Invalid windowBits: ' + opts.windowBits);
    }
  }

  if (opts.level) {
    if (opts.level < exports.Z_MIN_LEVEL || opts.level > exports.Z_MAX_LEVEL) {
      throw new Error('Invalid compression level: ' + opts.level);
    }
  }

  if (opts.memLevel) {
    if (opts.memLevel < exports.Z_MIN_MEMLEVEL || opts.memLevel > exports.Z_MAX_MEMLEVEL) {
      throw new Error('Invalid memLevel: ' + opts.memLevel);
    }
  }

  if (opts.strategy) {
    if (opts.strategy != exports.Z_FILTERED && opts.strategy != exports.Z_HUFFMAN_ONLY && opts.strategy != exports.Z_RLE && opts.strategy != exports.Z_FIXED && opts.strategy != exports.Z_DEFAULT_STRATEGY) {
      throw new Error('Invalid strategy: ' + opts.strategy);
    }
  }

  if (opts.dictionary) {
    if (!Buffer.isBuffer(opts.dictionary)) {
      throw new Error('Invalid dictionary: it should be a Buffer instance');
    }
  }

  this._handle = new binding.Zlib(mode);

  var self = this;
  this._hadError = false;
  this._handle.onerror = function (message, errno) {
    // there is no way to cleanly recover.
    // continuing only obscures problems.
    _close(self);
    self._hadError = true;

    var error = new Error(message);
    error.errno = errno;
    error.code = exports.codes[errno];
    self.emit('error', error);
  };

  var level = exports.Z_DEFAULT_COMPRESSION;
  if (typeof opts.level === 'number') level = opts.level;

  var strategy = exports.Z_DEFAULT_STRATEGY;
  if (typeof opts.strategy === 'number') strategy = opts.strategy;

  this._handle.init(opts.windowBits || exports.Z_DEFAULT_WINDOWBITS, level, opts.memLevel || exports.Z_DEFAULT_MEMLEVEL, strategy, opts.dictionary);

  this._buffer = Buffer.allocUnsafe(this._chunkSize);
  this._offset = 0;
  this._level = level;
  this._strategy = strategy;

  this.once('end', this.close);

  Object.defineProperty(this, '_closed', {
    get: function () {
      return !_this._handle;
    },
    configurable: true,
    enumerable: true
  });
}

util.inherits(Zlib, Transform);

Zlib.prototype.params = function (level, strategy, callback) {
  if (level < exports.Z_MIN_LEVEL || level > exports.Z_MAX_LEVEL) {
    throw new RangeError('Invalid compression level: ' + level);
  }
  if (strategy != exports.Z_FILTERED && strategy != exports.Z_HUFFMAN_ONLY && strategy != exports.Z_RLE && strategy != exports.Z_FIXED && strategy != exports.Z_DEFAULT_STRATEGY) {
    throw new TypeError('Invalid strategy: ' + strategy);
  }

  if (this._level !== level || this._strategy !== strategy) {
    var self = this;
    this.flush(binding.Z_SYNC_FLUSH, function () {
      assert(self._handle, 'zlib binding closed');
      self._handle.params(level, strategy);
      if (!self._hadError) {
        self._level = level;
        self._strategy = strategy;
        if (callback) callback();
      }
    });
  } else {
    process.nextTick(callback);
  }
};

Zlib.prototype.reset = function () {
  assert(this._handle, 'zlib binding closed');
  return this._handle.reset();
};

// This is the _flush function called by the transform class,
// internally, when the last chunk has been written.
Zlib.prototype._flush = function (callback) {
  this._transform(Buffer.alloc(0), '', callback);
};

Zlib.prototype.flush = function (kind, callback) {
  var _this2 = this;

  var ws = this._writableState;

  if (typeof kind === 'function' || kind === undefined && !callback) {
    callback = kind;
    kind = binding.Z_FULL_FLUSH;
  }

  if (ws.ended) {
    if (callback) process.nextTick(callback);
  } else if (ws.ending) {
    if (callback) this.once('end', callback);
  } else if (ws.needDrain) {
    if (callback) {
      this.once('drain', function () {
        return _this2.flush(kind, callback);
      });
    }
  } else {
    this._flushFlag = kind;
    this.write(Buffer.alloc(0), '', callback);
  }
};

Zlib.prototype.close = function (callback) {
  _close(this, callback);
  process.nextTick(emitCloseNT, this);
};

function _close(engine, callback) {
  if (callback) process.nextTick(callback);

  // Caller may invoke .close after a zlib error (which will null _handle).
  if (!engine._handle) return;

  engine._handle.close();
  engine._handle = null;
}

function emitCloseNT(self) {
  self.emit('close');
}

Zlib.prototype._transform = function (chunk, encoding, cb) {
  var flushFlag;
  var ws = this._writableState;
  var ending = ws.ending || ws.ended;
  var last = ending && (!chunk || ws.length === chunk.length);

  if (chunk !== null && !Buffer.isBuffer(chunk)) return cb(new Error('invalid input'));

  if (!this._handle) return cb(new Error('zlib binding closed'));

  // If it's the last chunk, or a final flush, we use the Z_FINISH flush flag
  // (or whatever flag was provided using opts.finishFlush).
  // If it's explicitly flushing at some other time, then we use
  // Z_FULL_FLUSH. Otherwise, use Z_NO_FLUSH for maximum compression
  // goodness.
  if (last) flushFlag = this._finishFlushFlag;else {
    flushFlag = this._flushFlag;
    // once we've flushed the last of the queue, stop flushing and
    // go back to the normal behavior.
    if (chunk.length >= ws.length) {
      this._flushFlag = this._opts.flush || binding.Z_NO_FLUSH;
    }
  }

  this._processChunk(chunk, flushFlag, cb);
};

Zlib.prototype._processChunk = function (chunk, flushFlag, cb) {
  var availInBefore = chunk && chunk.length;
  var availOutBefore = this._chunkSize - this._offset;
  var inOff = 0;

  var self = this;

  var async = typeof cb === 'function';

  if (!async) {
    var buffers = [];
    var nread = 0;

    var error;
    this.on('error', function (er) {
      error = er;
    });

    assert(this._handle, 'zlib binding closed');
    do {
      var res = this._handle.writeSync(flushFlag, chunk, // in
      inOff, // in_off
      availInBefore, // in_len
      this._buffer, // out
      this._offset, //out_off
      availOutBefore); // out_len
    } while (!this._hadError && callback(res[0], res[1]));

    if (this._hadError) {
      throw error;
    }

    if (nread >= kMaxLength) {
      _close(this);
      throw new RangeError(kRangeErrorMessage);
    }

    var buf = Buffer.concat(buffers, nread);
    _close(this);

    return buf;
  }

  assert(this._handle, 'zlib binding closed');
  var req = this._handle.write(flushFlag, chunk, // in
  inOff, // in_off
  availInBefore, // in_len
  this._buffer, // out
  this._offset, //out_off
  availOutBefore); // out_len

  req.buffer = chunk;
  req.callback = callback;

  function callback(availInAfter, availOutAfter) {
    // When the callback is used in an async write, the callback's
    // context is the `req` object that was created. The req object
    // is === this._handle, and that's why it's important to null
    // out the values after they are done being used. `this._handle`
    // can stay in memory longer than the callback and buffer are needed.
    if (this) {
      this.buffer = null;
      this.callback = null;
    }

    if (self._hadError) return;

    var have = availOutBefore - availOutAfter;
    assert(have >= 0, 'have should not go down');

    if (have > 0) {
      var out = self._buffer.slice(self._offset, self._offset + have);
      self._offset += have;
      // serve some output to the consumer.
      if (async) {
        self.push(out);
      } else {
        buffers.push(out);
        nread += out.length;
      }
    }

    // exhausted the output buffer, or used all the input create a new one.
    if (availOutAfter === 0 || self._offset >= self._chunkSize) {
      availOutBefore = self._chunkSize;
      self._offset = 0;
      self._buffer = Buffer.allocUnsafe(self._chunkSize);
    }

    if (availOutAfter === 0) {
      // Not actually done.  Need to reprocess.
      // Also, update the availInBefore to the availInAfter value,
      // so that if we have to hit it a third (fourth, etc.) time,
      // it'll have the correct byte counts.
      inOff += availInBefore - availInAfter;
      availInBefore = availInAfter;

      if (!async) return true;

      var newReq = self._handle.write(flushFlag, chunk, inOff, availInBefore, self._buffer, self._offset, self._chunkSize);
      newReq.callback = callback; // this same function
      newReq.buffer = chunk;
      return;
    }

    if (!async) return false;

    // finished with the chunk.
    cb();
  }
};

util.inherits(Deflate, Zlib);
util.inherits(Inflate, Zlib);
util.inherits(Gzip, Zlib);
util.inherits(Gunzip, Zlib);
util.inherits(DeflateRaw, Zlib);
util.inherits(InflateRaw, Zlib);
util.inherits(Unzip, Zlib);
/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(47)))

/***/ }),
/* 245 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(global) {

var objectAssign = __webpack_require__(351);

// compare and isBuffer taken from https://github.com/feross/buffer/blob/680e9e5e488f22aac27599a57dc844a6315928dd/index.js
// original notice:

/*!
 * The buffer module from node.js, for the browser.
 *
 * @author   Feross Aboukhadijeh <feross@feross.org> <http://feross.org>
 * @license  MIT
 */
function compare(a, b) {
  if (a === b) {
    return 0;
  }

  var x = a.length;
  var y = b.length;

  for (var i = 0, len = Math.min(x, y); i < len; ++i) {
    if (a[i] !== b[i]) {
      x = a[i];
      y = b[i];
      break;
    }
  }

  if (x < y) {
    return -1;
  }
  if (y < x) {
    return 1;
  }
  return 0;
}
function isBuffer(b) {
  if (global.Buffer && typeof global.Buffer.isBuffer === 'function') {
    return global.Buffer.isBuffer(b);
  }
  return !!(b != null && b._isBuffer);
}

// based on node assert, original notice:
// NB: The URL to the CommonJS spec is kept just for tradition.
//     node-assert has evolved a lot since then, both in API and behavior.

// http://wiki.commonjs.org/wiki/Unit_Testing/1.0
//
// THIS IS NOT TESTED NOR LIKELY TO WORK OUTSIDE V8!
//
// Originally from narwhal.js (http://narwhaljs.org)
// Copyright (c) 2009 Thomas Robinson <280north.com>
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the 'Software'), to
// deal in the Software without restriction, including without limitation the
// rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
// sell copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED 'AS IS', WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN
// ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
// WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

var util = __webpack_require__(166);
var hasOwn = Object.prototype.hasOwnProperty;
var pSlice = Array.prototype.slice;
var functionsHaveNames = (function () {
  return function foo() {}.name === 'foo';
}());
function pToString (obj) {
  return Object.prototype.toString.call(obj);
}
function isView(arrbuf) {
  if (isBuffer(arrbuf)) {
    return false;
  }
  if (typeof global.ArrayBuffer !== 'function') {
    return false;
  }
  if (typeof ArrayBuffer.isView === 'function') {
    return ArrayBuffer.isView(arrbuf);
  }
  if (!arrbuf) {
    return false;
  }
  if (arrbuf instanceof DataView) {
    return true;
  }
  if (arrbuf.buffer && arrbuf.buffer instanceof ArrayBuffer) {
    return true;
  }
  return false;
}
// 1. The assert module provides functions that throw
// AssertionError's when particular conditions are not met. The
// assert module must conform to the following interface.

var assert = module.exports = ok;

// 2. The AssertionError is defined in assert.
// new assert.AssertionError({ message: message,
//                             actual: actual,
//                             expected: expected })

var regex = /\s*function\s+([^\(\s]*)\s*/;
// based on https://github.com/ljharb/function.prototype.name/blob/adeeeec8bfcc6068b187d7d9fb3d5bb1d3a30899/implementation.js
function getName(func) {
  if (!util.isFunction(func)) {
    return;
  }
  if (functionsHaveNames) {
    return func.name;
  }
  var str = func.toString();
  var match = str.match(regex);
  return match && match[1];
}
assert.AssertionError = function AssertionError(options) {
  this.name = 'AssertionError';
  this.actual = options.actual;
  this.expected = options.expected;
  this.operator = options.operator;
  if (options.message) {
    this.message = options.message;
    this.generatedMessage = false;
  } else {
    this.message = getMessage(this);
    this.generatedMessage = true;
  }
  var stackStartFunction = options.stackStartFunction || fail;
  if (Error.captureStackTrace) {
    Error.captureStackTrace(this, stackStartFunction);
  } else {
    // non v8 browsers so we can have a stacktrace
    var err = new Error();
    if (err.stack) {
      var out = err.stack;

      // try to strip useless frames
      var fn_name = getName(stackStartFunction);
      var idx = out.indexOf('\n' + fn_name);
      if (idx >= 0) {
        // once we have located the function frame
        // we need to strip out everything before it (and its line)
        var next_line = out.indexOf('\n', idx + 1);
        out = out.substring(next_line + 1);
      }

      this.stack = out;
    }
  }
};

// assert.AssertionError instanceof Error
util.inherits(assert.AssertionError, Error);

function truncate(s, n) {
  if (typeof s === 'string') {
    return s.length < n ? s : s.slice(0, n);
  } else {
    return s;
  }
}
function inspect(something) {
  if (functionsHaveNames || !util.isFunction(something)) {
    return util.inspect(something);
  }
  var rawname = getName(something);
  var name = rawname ? ': ' + rawname : '';
  return '[Function' +  name + ']';
}
function getMessage(self) {
  return truncate(inspect(self.actual), 128) + ' ' +
         self.operator + ' ' +
         truncate(inspect(self.expected), 128);
}

// At present only the three keys mentioned above are used and
// understood by the spec. Implementations or sub modules can pass
// other keys to the AssertionError's constructor - they will be
// ignored.

// 3. All of the following functions must throw an AssertionError
// when a corresponding condition is not met, with a message that
// may be undefined if not provided.  All assertion methods provide
// both the actual and expected values to the assertion error for
// display purposes.

function fail(actual, expected, message, operator, stackStartFunction) {
  throw new assert.AssertionError({
    message: message,
    actual: actual,
    expected: expected,
    operator: operator,
    stackStartFunction: stackStartFunction
  });
}

// EXTENSION! allows for well behaved errors defined elsewhere.
assert.fail = fail;

// 4. Pure assertion tests whether a value is truthy, as determined
// by !!guard.
// assert.ok(guard, message_opt);
// This statement is equivalent to assert.equal(true, !!guard,
// message_opt);. To test strictly for the value true, use
// assert.strictEqual(true, guard, message_opt);.

function ok(value, message) {
  if (!value) fail(value, true, message, '==', assert.ok);
}
assert.ok = ok;

// 5. The equality assertion tests shallow, coercive equality with
// ==.
// assert.equal(actual, expected, message_opt);

assert.equal = function equal(actual, expected, message) {
  if (actual != expected) fail(actual, expected, message, '==', assert.equal);
};

// 6. The non-equality assertion tests for whether two objects are not equal
// with != assert.notEqual(actual, expected, message_opt);

assert.notEqual = function notEqual(actual, expected, message) {
  if (actual == expected) {
    fail(actual, expected, message, '!=', assert.notEqual);
  }
};

// 7. The equivalence assertion tests a deep equality relation.
// assert.deepEqual(actual, expected, message_opt);

assert.deepEqual = function deepEqual(actual, expected, message) {
  if (!_deepEqual(actual, expected, false)) {
    fail(actual, expected, message, 'deepEqual', assert.deepEqual);
  }
};

assert.deepStrictEqual = function deepStrictEqual(actual, expected, message) {
  if (!_deepEqual(actual, expected, true)) {
    fail(actual, expected, message, 'deepStrictEqual', assert.deepStrictEqual);
  }
};

function _deepEqual(actual, expected, strict, memos) {
  // 7.1. All identical values are equivalent, as determined by ===.
  if (actual === expected) {
    return true;
  } else if (isBuffer(actual) && isBuffer(expected)) {
    return compare(actual, expected) === 0;

  // 7.2. If the expected value is a Date object, the actual value is
  // equivalent if it is also a Date object that refers to the same time.
  } else if (util.isDate(actual) && util.isDate(expected)) {
    return actual.getTime() === expected.getTime();

  // 7.3 If the expected value is a RegExp object, the actual value is
  // equivalent if it is also a RegExp object with the same source and
  // properties (`global`, `multiline`, `lastIndex`, `ignoreCase`).
  } else if (util.isRegExp(actual) && util.isRegExp(expected)) {
    return actual.source === expected.source &&
           actual.global === expected.global &&
           actual.multiline === expected.multiline &&
           actual.lastIndex === expected.lastIndex &&
           actual.ignoreCase === expected.ignoreCase;

  // 7.4. Other pairs that do not both pass typeof value == 'object',
  // equivalence is determined by ==.
  } else if ((actual === null || typeof actual !== 'object') &&
             (expected === null || typeof expected !== 'object')) {
    return strict ? actual === expected : actual == expected;

  // If both values are instances of typed arrays, wrap their underlying
  // ArrayBuffers in a Buffer each to increase performance
  // This optimization requires the arrays to have the same type as checked by
  // Object.prototype.toString (aka pToString). Never perform binary
  // comparisons for Float*Arrays, though, since e.g. +0 === -0 but their
  // bit patterns are not identical.
  } else if (isView(actual) && isView(expected) &&
             pToString(actual) === pToString(expected) &&
             !(actual instanceof Float32Array ||
               actual instanceof Float64Array)) {
    return compare(new Uint8Array(actual.buffer),
                   new Uint8Array(expected.buffer)) === 0;

  // 7.5 For all other Object pairs, including Array objects, equivalence is
  // determined by having the same number of owned properties (as verified
  // with Object.prototype.hasOwnProperty.call), the same set of keys
  // (although not necessarily the same order), equivalent values for every
  // corresponding key, and an identical 'prototype' property. Note: this
  // accounts for both named and indexed properties on Arrays.
  } else if (isBuffer(actual) !== isBuffer(expected)) {
    return false;
  } else {
    memos = memos || {actual: [], expected: []};

    var actualIndex = memos.actual.indexOf(actual);
    if (actualIndex !== -1) {
      if (actualIndex === memos.expected.indexOf(expected)) {
        return true;
      }
    }

    memos.actual.push(actual);
    memos.expected.push(expected);

    return objEquiv(actual, expected, strict, memos);
  }
}

function isArguments(object) {
  return Object.prototype.toString.call(object) == '[object Arguments]';
}

function objEquiv(a, b, strict, actualVisitedObjects) {
  if (a === null || a === undefined || b === null || b === undefined)
    return false;
  // if one is a primitive, the other must be same
  if (util.isPrimitive(a) || util.isPrimitive(b))
    return a === b;
  if (strict && Object.getPrototypeOf(a) !== Object.getPrototypeOf(b))
    return false;
  var aIsArgs = isArguments(a);
  var bIsArgs = isArguments(b);
  if ((aIsArgs && !bIsArgs) || (!aIsArgs && bIsArgs))
    return false;
  if (aIsArgs) {
    a = pSlice.call(a);
    b = pSlice.call(b);
    return _deepEqual(a, b, strict);
  }
  var ka = objectKeys(a);
  var kb = objectKeys(b);
  var key, i;
  // having the same number of owned properties (keys incorporates
  // hasOwnProperty)
  if (ka.length !== kb.length)
    return false;
  //the same set of keys (although not necessarily the same order),
  ka.sort();
  kb.sort();
  //~~~cheap key test
  for (i = ka.length - 1; i >= 0; i--) {
    if (ka[i] !== kb[i])
      return false;
  }
  //equivalent values for every corresponding key, and
  //~~~possibly expensive deep test
  for (i = ka.length - 1; i >= 0; i--) {
    key = ka[i];
    if (!_deepEqual(a[key], b[key], strict, actualVisitedObjects))
      return false;
  }
  return true;
}

// 8. The non-equivalence assertion tests for any deep inequality.
// assert.notDeepEqual(actual, expected, message_opt);

assert.notDeepEqual = function notDeepEqual(actual, expected, message) {
  if (_deepEqual(actual, expected, false)) {
    fail(actual, expected, message, 'notDeepEqual', assert.notDeepEqual);
  }
};

assert.notDeepStrictEqual = notDeepStrictEqual;
function notDeepStrictEqual(actual, expected, message) {
  if (_deepEqual(actual, expected, true)) {
    fail(actual, expected, message, 'notDeepStrictEqual', notDeepStrictEqual);
  }
}


// 9. The strict equality assertion tests strict equality, as determined by ===.
// assert.strictEqual(actual, expected, message_opt);

assert.strictEqual = function strictEqual(actual, expected, message) {
  if (actual !== expected) {
    fail(actual, expected, message, '===', assert.strictEqual);
  }
};

// 10. The strict non-equality assertion tests for strict inequality, as
// determined by !==.  assert.notStrictEqual(actual, expected, message_opt);

assert.notStrictEqual = function notStrictEqual(actual, expected, message) {
  if (actual === expected) {
    fail(actual, expected, message, '!==', assert.notStrictEqual);
  }
};

function expectedException(actual, expected) {
  if (!actual || !expected) {
    return false;
  }

  if (Object.prototype.toString.call(expected) == '[object RegExp]') {
    return expected.test(actual);
  }

  try {
    if (actual instanceof expected) {
      return true;
    }
  } catch (e) {
    // Ignore.  The instanceof check doesn't work for arrow functions.
  }

  if (Error.isPrototypeOf(expected)) {
    return false;
  }

  return expected.call({}, actual) === true;
}

function _tryBlock(block) {
  var error;
  try {
    block();
  } catch (e) {
    error = e;
  }
  return error;
}

function _throws(shouldThrow, block, expected, message) {
  var actual;

  if (typeof block !== 'function') {
    throw new TypeError('"block" argument must be a function');
  }

  if (typeof expected === 'string') {
    message = expected;
    expected = null;
  }

  actual = _tryBlock(block);

  message = (expected && expected.name ? ' (' + expected.name + ').' : '.') +
            (message ? ' ' + message : '.');

  if (shouldThrow && !actual) {
    fail(actual, expected, 'Missing expected exception' + message);
  }

  var userProvidedMessage = typeof message === 'string';
  var isUnwantedException = !shouldThrow && util.isError(actual);
  var isUnexpectedException = !shouldThrow && actual && !expected;

  if ((isUnwantedException &&
      userProvidedMessage &&
      expectedException(actual, expected)) ||
      isUnexpectedException) {
    fail(actual, expected, 'Got unwanted exception' + message);
  }

  if ((shouldThrow && actual && expected &&
      !expectedException(actual, expected)) || (!shouldThrow && actual)) {
    throw actual;
  }
}

// 11. Expected to throw an error:
// assert.throws(block, Error_opt, message_opt);

assert.throws = function(block, /*optional*/error, /*optional*/message) {
  _throws(true, block, error, message);
};

// EXTENSION! This is annoying to write outside this module.
assert.doesNotThrow = function(block, /*optional*/error, /*optional*/message) {
  _throws(false, block, error, message);
};

assert.ifError = function(err) { if (err) throw err; };

// Expose a strict only variant of assert
function strict(value, message) {
  if (!value) fail(value, true, message, '==', strict);
}
assert.strict = objectAssign(strict, assert, {
  equal: assert.strictEqual,
  deepEqual: assert.deepStrictEqual,
  notEqual: assert.notStrictEqual,
  notDeepEqual: assert.notDeepStrictEqual
});
assert.strict.strict = assert.strict;

var objectKeys = Object.keys || function (obj) {
  var keys = [];
  for (var key in obj) {
    if (hasOwn.call(obj, key)) keys.push(key);
  }
  return keys;
};

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(24)))

/***/ }),
/* 246 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// Note: adler32 takes 12% for level 0 and 2% for level 6.
// It isn't worth it to make additional optimizations as in original.
// Small size is preferable.

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

function adler32(adler, buf, len, pos) {
  var s1 = (adler & 0xffff) |0,
      s2 = ((adler >>> 16) & 0xffff) |0,
      n = 0;

  while (len !== 0) {
    // Set limit ~ twice less than 5552, to keep
    // s2 in 31-bits, because we force signed ints.
    // in other case %= will fail.
    n = len > 2000 ? 2000 : len;
    len -= n;

    do {
      s1 = (s1 + buf[pos++]) |0;
      s2 = (s2 + s1) |0;
    } while (--n);

    s1 %= 65521;
    s2 %= 65521;
  }

  return (s1 | (s2 << 16)) |0;
}


module.exports = adler32;


/***/ }),
/* 247 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// Note: we can't get significant speed boost here.
// So write code to minimize size - no pregenerated tables
// and array tools dependencies.

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

// Use ordinary array, since untyped makes no boost here
function makeTable() {
  var c, table = [];

  for (var n = 0; n < 256; n++) {
    c = n;
    for (var k = 0; k < 8; k++) {
      c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
    }
    table[n] = c;
  }

  return table;
}

// Create table on load. Just 255 signed longs. Not a problem.
var crcTable = makeTable();


function crc32(crc, buf, len, pos) {
  var t = crcTable,
      end = pos + len;

  crc ^= -1;

  for (var i = pos; i < end; i++) {
    crc = (crc >>> 8) ^ t[(crc ^ buf[i]) & 0xFF];
  }

  return (crc ^ (-1)); // >>> 0;
}


module.exports = crc32;


/***/ }),
/* 248 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2));
	}
	else {}
}(this, function (CryptoJS) {

	(function (Math) {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var WordArray = C_lib.WordArray;
	    var Hasher = C_lib.Hasher;
	    var C_algo = C.algo;

	    // Initialization and round constants tables
	    var H = [];
	    var K = [];

	    // Compute constants
	    (function () {
	        function isPrime(n) {
	            var sqrtN = Math.sqrt(n);
	            for (var factor = 2; factor <= sqrtN; factor++) {
	                if (!(n % factor)) {
	                    return false;
	                }
	            }

	            return true;
	        }

	        function getFractionalBits(n) {
	            return ((n - (n | 0)) * 0x100000000) | 0;
	        }

	        var n = 2;
	        var nPrime = 0;
	        while (nPrime < 64) {
	            if (isPrime(n)) {
	                if (nPrime < 8) {
	                    H[nPrime] = getFractionalBits(Math.pow(n, 1 / 2));
	                }
	                K[nPrime] = getFractionalBits(Math.pow(n, 1 / 3));

	                nPrime++;
	            }

	            n++;
	        }
	    }());

	    // Reusable object
	    var W = [];

	    /**
	     * SHA-256 hash algorithm.
	     */
	    var SHA256 = C_algo.SHA256 = Hasher.extend({
	        _doReset: function () {
	            this._hash = new WordArray.init(H.slice(0));
	        },

	        _doProcessBlock: function (M, offset) {
	            // Shortcut
	            var H = this._hash.words;

	            // Working variables
	            var a = H[0];
	            var b = H[1];
	            var c = H[2];
	            var d = H[3];
	            var e = H[4];
	            var f = H[5];
	            var g = H[6];
	            var h = H[7];

	            // Computation
	            for (var i = 0; i < 64; i++) {
	                if (i < 16) {
	                    W[i] = M[offset + i] | 0;
	                } else {
	                    var gamma0x = W[i - 15];
	                    var gamma0  = ((gamma0x << 25) | (gamma0x >>> 7))  ^
	                                  ((gamma0x << 14) | (gamma0x >>> 18)) ^
	                                   (gamma0x >>> 3);

	                    var gamma1x = W[i - 2];
	                    var gamma1  = ((gamma1x << 15) | (gamma1x >>> 17)) ^
	                                  ((gamma1x << 13) | (gamma1x >>> 19)) ^
	                                   (gamma1x >>> 10);

	                    W[i] = gamma0 + W[i - 7] + gamma1 + W[i - 16];
	                }

	                var ch  = (e & f) ^ (~e & g);
	                var maj = (a & b) ^ (a & c) ^ (b & c);

	                var sigma0 = ((a << 30) | (a >>> 2)) ^ ((a << 19) | (a >>> 13)) ^ ((a << 10) | (a >>> 22));
	                var sigma1 = ((e << 26) | (e >>> 6)) ^ ((e << 21) | (e >>> 11)) ^ ((e << 7)  | (e >>> 25));

	                var t1 = h + sigma1 + ch + K[i] + W[i];
	                var t2 = sigma0 + maj;

	                h = g;
	                g = f;
	                f = e;
	                e = (d + t1) | 0;
	                d = c;
	                c = b;
	                b = a;
	                a = (t1 + t2) | 0;
	            }

	            // Intermediate hash value
	            H[0] = (H[0] + a) | 0;
	            H[1] = (H[1] + b) | 0;
	            H[2] = (H[2] + c) | 0;
	            H[3] = (H[3] + d) | 0;
	            H[4] = (H[4] + e) | 0;
	            H[5] = (H[5] + f) | 0;
	            H[6] = (H[6] + g) | 0;
	            H[7] = (H[7] + h) | 0;
	        },

	        _doFinalize: function () {
	            // Shortcuts
	            var data = this._data;
	            var dataWords = data.words;

	            var nBitsTotal = this._nDataBytes * 8;
	            var nBitsLeft = data.sigBytes * 8;

	            // Add padding
	            dataWords[nBitsLeft >>> 5] |= 0x80 << (24 - nBitsLeft % 32);
	            dataWords[(((nBitsLeft + 64) >>> 9) << 4) + 14] = Math.floor(nBitsTotal / 0x100000000);
	            dataWords[(((nBitsLeft + 64) >>> 9) << 4) + 15] = nBitsTotal;
	            data.sigBytes = dataWords.length * 4;

	            // Hash final blocks
	            this._process();

	            // Return final computed hash
	            return this._hash;
	        },

	        clone: function () {
	            var clone = Hasher.clone.call(this);
	            clone._hash = this._hash.clone();

	            return clone;
	        }
	    });

	    /**
	     * Shortcut function to the hasher's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     *
	     * @return {WordArray} The hash.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hash = CryptoJS.SHA256('message');
	     *     var hash = CryptoJS.SHA256(wordArray);
	     */
	    C.SHA256 = Hasher._createHelper(SHA256);

	    /**
	     * Shortcut function to the HMAC's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     * @param {WordArray|string} key The secret key.
	     *
	     * @return {WordArray} The HMAC.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hmac = CryptoJS.HmacSHA256(message, key);
	     */
	    C.HmacSHA256 = Hasher._createHmacHelper(SHA256);
	}(Math));


	return CryptoJS.SHA256;

}));

/***/ }),
/* 249 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(127));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var Hasher = C_lib.Hasher;
	    var C_x64 = C.x64;
	    var X64Word = C_x64.Word;
	    var X64WordArray = C_x64.WordArray;
	    var C_algo = C.algo;

	    function X64Word_create() {
	        return X64Word.create.apply(X64Word, arguments);
	    }

	    // Constants
	    var K = [
	        X64Word_create(0x428a2f98, 0xd728ae22), X64Word_create(0x71374491, 0x23ef65cd),
	        X64Word_create(0xb5c0fbcf, 0xec4d3b2f), X64Word_create(0xe9b5dba5, 0x8189dbbc),
	        X64Word_create(0x3956c25b, 0xf348b538), X64Word_create(0x59f111f1, 0xb605d019),
	        X64Word_create(0x923f82a4, 0xaf194f9b), X64Word_create(0xab1c5ed5, 0xda6d8118),
	        X64Word_create(0xd807aa98, 0xa3030242), X64Word_create(0x12835b01, 0x45706fbe),
	        X64Word_create(0x243185be, 0x4ee4b28c), X64Word_create(0x550c7dc3, 0xd5ffb4e2),
	        X64Word_create(0x72be5d74, 0xf27b896f), X64Word_create(0x80deb1fe, 0x3b1696b1),
	        X64Word_create(0x9bdc06a7, 0x25c71235), X64Word_create(0xc19bf174, 0xcf692694),
	        X64Word_create(0xe49b69c1, 0x9ef14ad2), X64Word_create(0xefbe4786, 0x384f25e3),
	        X64Word_create(0x0fc19dc6, 0x8b8cd5b5), X64Word_create(0x240ca1cc, 0x77ac9c65),
	        X64Word_create(0x2de92c6f, 0x592b0275), X64Word_create(0x4a7484aa, 0x6ea6e483),
	        X64Word_create(0x5cb0a9dc, 0xbd41fbd4), X64Word_create(0x76f988da, 0x831153b5),
	        X64Word_create(0x983e5152, 0xee66dfab), X64Word_create(0xa831c66d, 0x2db43210),
	        X64Word_create(0xb00327c8, 0x98fb213f), X64Word_create(0xbf597fc7, 0xbeef0ee4),
	        X64Word_create(0xc6e00bf3, 0x3da88fc2), X64Word_create(0xd5a79147, 0x930aa725),
	        X64Word_create(0x06ca6351, 0xe003826f), X64Word_create(0x14292967, 0x0a0e6e70),
	        X64Word_create(0x27b70a85, 0x46d22ffc), X64Word_create(0x2e1b2138, 0x5c26c926),
	        X64Word_create(0x4d2c6dfc, 0x5ac42aed), X64Word_create(0x53380d13, 0x9d95b3df),
	        X64Word_create(0x650a7354, 0x8baf63de), X64Word_create(0x766a0abb, 0x3c77b2a8),
	        X64Word_create(0x81c2c92e, 0x47edaee6), X64Word_create(0x92722c85, 0x1482353b),
	        X64Word_create(0xa2bfe8a1, 0x4cf10364), X64Word_create(0xa81a664b, 0xbc423001),
	        X64Word_create(0xc24b8b70, 0xd0f89791), X64Word_create(0xc76c51a3, 0x0654be30),
	        X64Word_create(0xd192e819, 0xd6ef5218), X64Word_create(0xd6990624, 0x5565a910),
	        X64Word_create(0xf40e3585, 0x5771202a), X64Word_create(0x106aa070, 0x32bbd1b8),
	        X64Word_create(0x19a4c116, 0xb8d2d0c8), X64Word_create(0x1e376c08, 0x5141ab53),
	        X64Word_create(0x2748774c, 0xdf8eeb99), X64Word_create(0x34b0bcb5, 0xe19b48a8),
	        X64Word_create(0x391c0cb3, 0xc5c95a63), X64Word_create(0x4ed8aa4a, 0xe3418acb),
	        X64Word_create(0x5b9cca4f, 0x7763e373), X64Word_create(0x682e6ff3, 0xd6b2b8a3),
	        X64Word_create(0x748f82ee, 0x5defb2fc), X64Word_create(0x78a5636f, 0x43172f60),
	        X64Word_create(0x84c87814, 0xa1f0ab72), X64Word_create(0x8cc70208, 0x1a6439ec),
	        X64Word_create(0x90befffa, 0x23631e28), X64Word_create(0xa4506ceb, 0xde82bde9),
	        X64Word_create(0xbef9a3f7, 0xb2c67915), X64Word_create(0xc67178f2, 0xe372532b),
	        X64Word_create(0xca273ece, 0xea26619c), X64Word_create(0xd186b8c7, 0x21c0c207),
	        X64Word_create(0xeada7dd6, 0xcde0eb1e), X64Word_create(0xf57d4f7f, 0xee6ed178),
	        X64Word_create(0x06f067aa, 0x72176fba), X64Word_create(0x0a637dc5, 0xa2c898a6),
	        X64Word_create(0x113f9804, 0xbef90dae), X64Word_create(0x1b710b35, 0x131c471b),
	        X64Word_create(0x28db77f5, 0x23047d84), X64Word_create(0x32caab7b, 0x40c72493),
	        X64Word_create(0x3c9ebe0a, 0x15c9bebc), X64Word_create(0x431d67c4, 0x9c100d4c),
	        X64Word_create(0x4cc5d4be, 0xcb3e42b6), X64Word_create(0x597f299c, 0xfc657e2a),
	        X64Word_create(0x5fcb6fab, 0x3ad6faec), X64Word_create(0x6c44198c, 0x4a475817)
	    ];

	    // Reusable objects
	    var W = [];
	    (function () {
	        for (var i = 0; i < 80; i++) {
	            W[i] = X64Word_create();
	        }
	    }());

	    /**
	     * SHA-512 hash algorithm.
	     */
	    var SHA512 = C_algo.SHA512 = Hasher.extend({
	        _doReset: function () {
	            this._hash = new X64WordArray.init([
	                new X64Word.init(0x6a09e667, 0xf3bcc908), new X64Word.init(0xbb67ae85, 0x84caa73b),
	                new X64Word.init(0x3c6ef372, 0xfe94f82b), new X64Word.init(0xa54ff53a, 0x5f1d36f1),
	                new X64Word.init(0x510e527f, 0xade682d1), new X64Word.init(0x9b05688c, 0x2b3e6c1f),
	                new X64Word.init(0x1f83d9ab, 0xfb41bd6b), new X64Word.init(0x5be0cd19, 0x137e2179)
	            ]);
	        },

	        _doProcessBlock: function (M, offset) {
	            // Shortcuts
	            var H = this._hash.words;

	            var H0 = H[0];
	            var H1 = H[1];
	            var H2 = H[2];
	            var H3 = H[3];
	            var H4 = H[4];
	            var H5 = H[5];
	            var H6 = H[6];
	            var H7 = H[7];

	            var H0h = H0.high;
	            var H0l = H0.low;
	            var H1h = H1.high;
	            var H1l = H1.low;
	            var H2h = H2.high;
	            var H2l = H2.low;
	            var H3h = H3.high;
	            var H3l = H3.low;
	            var H4h = H4.high;
	            var H4l = H4.low;
	            var H5h = H5.high;
	            var H5l = H5.low;
	            var H6h = H6.high;
	            var H6l = H6.low;
	            var H7h = H7.high;
	            var H7l = H7.low;

	            // Working variables
	            var ah = H0h;
	            var al = H0l;
	            var bh = H1h;
	            var bl = H1l;
	            var ch = H2h;
	            var cl = H2l;
	            var dh = H3h;
	            var dl = H3l;
	            var eh = H4h;
	            var el = H4l;
	            var fh = H5h;
	            var fl = H5l;
	            var gh = H6h;
	            var gl = H6l;
	            var hh = H7h;
	            var hl = H7l;

	            // Rounds
	            for (var i = 0; i < 80; i++) {
	                // Shortcut
	                var Wi = W[i];

	                // Extend message
	                if (i < 16) {
	                    var Wih = Wi.high = M[offset + i * 2]     | 0;
	                    var Wil = Wi.low  = M[offset + i * 2 + 1] | 0;
	                } else {
	                    // Gamma0
	                    var gamma0x  = W[i - 15];
	                    var gamma0xh = gamma0x.high;
	                    var gamma0xl = gamma0x.low;
	                    var gamma0h  = ((gamma0xh >>> 1) | (gamma0xl << 31)) ^ ((gamma0xh >>> 8) | (gamma0xl << 24)) ^ (gamma0xh >>> 7);
	                    var gamma0l  = ((gamma0xl >>> 1) | (gamma0xh << 31)) ^ ((gamma0xl >>> 8) | (gamma0xh << 24)) ^ ((gamma0xl >>> 7) | (gamma0xh << 25));

	                    // Gamma1
	                    var gamma1x  = W[i - 2];
	                    var gamma1xh = gamma1x.high;
	                    var gamma1xl = gamma1x.low;
	                    var gamma1h  = ((gamma1xh >>> 19) | (gamma1xl << 13)) ^ ((gamma1xh << 3) | (gamma1xl >>> 29)) ^ (gamma1xh >>> 6);
	                    var gamma1l  = ((gamma1xl >>> 19) | (gamma1xh << 13)) ^ ((gamma1xl << 3) | (gamma1xh >>> 29)) ^ ((gamma1xl >>> 6) | (gamma1xh << 26));

	                    // W[i] = gamma0 + W[i - 7] + gamma1 + W[i - 16]
	                    var Wi7  = W[i - 7];
	                    var Wi7h = Wi7.high;
	                    var Wi7l = Wi7.low;

	                    var Wi16  = W[i - 16];
	                    var Wi16h = Wi16.high;
	                    var Wi16l = Wi16.low;

	                    var Wil = gamma0l + Wi7l;
	                    var Wih = gamma0h + Wi7h + ((Wil >>> 0) < (gamma0l >>> 0) ? 1 : 0);
	                    var Wil = Wil + gamma1l;
	                    var Wih = Wih + gamma1h + ((Wil >>> 0) < (gamma1l >>> 0) ? 1 : 0);
	                    var Wil = Wil + Wi16l;
	                    var Wih = Wih + Wi16h + ((Wil >>> 0) < (Wi16l >>> 0) ? 1 : 0);

	                    Wi.high = Wih;
	                    Wi.low  = Wil;
	                }

	                var chh  = (eh & fh) ^ (~eh & gh);
	                var chl  = (el & fl) ^ (~el & gl);
	                var majh = (ah & bh) ^ (ah & ch) ^ (bh & ch);
	                var majl = (al & bl) ^ (al & cl) ^ (bl & cl);

	                var sigma0h = ((ah >>> 28) | (al << 4))  ^ ((ah << 30)  | (al >>> 2)) ^ ((ah << 25) | (al >>> 7));
	                var sigma0l = ((al >>> 28) | (ah << 4))  ^ ((al << 30)  | (ah >>> 2)) ^ ((al << 25) | (ah >>> 7));
	                var sigma1h = ((eh >>> 14) | (el << 18)) ^ ((eh >>> 18) | (el << 14)) ^ ((eh << 23) | (el >>> 9));
	                var sigma1l = ((el >>> 14) | (eh << 18)) ^ ((el >>> 18) | (eh << 14)) ^ ((el << 23) | (eh >>> 9));

	                // t1 = h + sigma1 + ch + K[i] + W[i]
	                var Ki  = K[i];
	                var Kih = Ki.high;
	                var Kil = Ki.low;

	                var t1l = hl + sigma1l;
	                var t1h = hh + sigma1h + ((t1l >>> 0) < (hl >>> 0) ? 1 : 0);
	                var t1l = t1l + chl;
	                var t1h = t1h + chh + ((t1l >>> 0) < (chl >>> 0) ? 1 : 0);
	                var t1l = t1l + Kil;
	                var t1h = t1h + Kih + ((t1l >>> 0) < (Kil >>> 0) ? 1 : 0);
	                var t1l = t1l + Wil;
	                var t1h = t1h + Wih + ((t1l >>> 0) < (Wil >>> 0) ? 1 : 0);

	                // t2 = sigma0 + maj
	                var t2l = sigma0l + majl;
	                var t2h = sigma0h + majh + ((t2l >>> 0) < (sigma0l >>> 0) ? 1 : 0);

	                // Update working variables
	                hh = gh;
	                hl = gl;
	                gh = fh;
	                gl = fl;
	                fh = eh;
	                fl = el;
	                el = (dl + t1l) | 0;
	                eh = (dh + t1h + ((el >>> 0) < (dl >>> 0) ? 1 : 0)) | 0;
	                dh = ch;
	                dl = cl;
	                ch = bh;
	                cl = bl;
	                bh = ah;
	                bl = al;
	                al = (t1l + t2l) | 0;
	                ah = (t1h + t2h + ((al >>> 0) < (t1l >>> 0) ? 1 : 0)) | 0;
	            }

	            // Intermediate hash value
	            H0l = H0.low  = (H0l + al);
	            H0.high = (H0h + ah + ((H0l >>> 0) < (al >>> 0) ? 1 : 0));
	            H1l = H1.low  = (H1l + bl);
	            H1.high = (H1h + bh + ((H1l >>> 0) < (bl >>> 0) ? 1 : 0));
	            H2l = H2.low  = (H2l + cl);
	            H2.high = (H2h + ch + ((H2l >>> 0) < (cl >>> 0) ? 1 : 0));
	            H3l = H3.low  = (H3l + dl);
	            H3.high = (H3h + dh + ((H3l >>> 0) < (dl >>> 0) ? 1 : 0));
	            H4l = H4.low  = (H4l + el);
	            H4.high = (H4h + eh + ((H4l >>> 0) < (el >>> 0) ? 1 : 0));
	            H5l = H5.low  = (H5l + fl);
	            H5.high = (H5h + fh + ((H5l >>> 0) < (fl >>> 0) ? 1 : 0));
	            H6l = H6.low  = (H6l + gl);
	            H6.high = (H6h + gh + ((H6l >>> 0) < (gl >>> 0) ? 1 : 0));
	            H7l = H7.low  = (H7l + hl);
	            H7.high = (H7h + hh + ((H7l >>> 0) < (hl >>> 0) ? 1 : 0));
	        },

	        _doFinalize: function () {
	            // Shortcuts
	            var data = this._data;
	            var dataWords = data.words;

	            var nBitsTotal = this._nDataBytes * 8;
	            var nBitsLeft = data.sigBytes * 8;

	            // Add padding
	            dataWords[nBitsLeft >>> 5] |= 0x80 << (24 - nBitsLeft % 32);
	            dataWords[(((nBitsLeft + 128) >>> 10) << 5) + 30] = Math.floor(nBitsTotal / 0x100000000);
	            dataWords[(((nBitsLeft + 128) >>> 10) << 5) + 31] = nBitsTotal;
	            data.sigBytes = dataWords.length * 4;

	            // Hash final blocks
	            this._process();

	            // Convert hash to 32-bit word array before returning
	            var hash = this._hash.toX32();

	            // Return final computed hash
	            return hash;
	        },

	        clone: function () {
	            var clone = Hasher.clone.call(this);
	            clone._hash = this._hash.clone();

	            return clone;
	        },

	        blockSize: 1024/32
	    });

	    /**
	     * Shortcut function to the hasher's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     *
	     * @return {WordArray} The hash.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hash = CryptoJS.SHA512('message');
	     *     var hash = CryptoJS.SHA512(wordArray);
	     */
	    C.SHA512 = Hasher._createHelper(SHA512);

	    /**
	     * Shortcut function to the HMAC's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     * @param {WordArray|string} key The secret key.
	     *
	     * @return {WordArray} The HMAC.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hmac = CryptoJS.HmacSHA512(message, key);
	     */
	    C.HmacSHA512 = Hasher._createHmacHelper(SHA512);
	}());


	return CryptoJS.SHA512;

}));

/***/ }),
/* 250 */
/***/ (function(module) {

module.exports = JSON.parse("[[\"a140\",\"\",62],[\"a180\",\"\",32],[\"a240\",\"\",62],[\"a280\",\"\",32],[\"a2ab\",\"\",5],[\"a2e3\",\"€\"],[\"a2ef\",\"\"],[\"a2fd\",\"\"],[\"a340\",\"\",62],[\"a380\",\"\",31,\"　\"],[\"a440\",\"\",62],[\"a480\",\"\",32],[\"a4f4\",\"\",10],[\"a540\",\"\",62],[\"a580\",\"\",32],[\"a5f7\",\"\",7],[\"a640\",\"\",62],[\"a680\",\"\",32],[\"a6b9\",\"\",7],[\"a6d9\",\"\",6],[\"a6ec\",\"\"],[\"a6f3\",\"\"],[\"a6f6\",\"\",8],[\"a740\",\"\",62],[\"a780\",\"\",32],[\"a7c2\",\"\",14],[\"a7f2\",\"\",12],[\"a896\",\"\",10],[\"a8bc\",\"ḿ\"],[\"a8bf\",\"ǹ\"],[\"a8c1\",\"\"],[\"a8ea\",\"\",20],[\"a958\",\"\"],[\"a95b\",\"\"],[\"a95d\",\"\"],[\"a989\",\"〾⿰\",11],[\"a997\",\"\",12],[\"a9f0\",\"\",14],[\"aaa1\",\"\",93],[\"aba1\",\"\",93],[\"aca1\",\"\",93],[\"ada1\",\"\",93],[\"aea1\",\"\",93],[\"afa1\",\"\",93],[\"d7fa\",\"\",4],[\"f8a1\",\"\",93],[\"f9a1\",\"\",93],[\"faa1\",\"\",93],[\"fba1\",\"\",93],[\"fca1\",\"\",93],[\"fda1\",\"\",93],[\"fe50\",\"⺁⺄㑳㑇⺈⺋㖞㘚㘎⺌⺗㥮㤘㧏㧟㩳㧐㭎㱮㳠⺧⺪䁖䅟⺮䌷⺳⺶⺷䎱䎬⺻䏝䓖䙡䙌\"],[\"fe80\",\"䜣䜩䝼䞍⻊䥇䥺䥽䦂䦃䦅䦆䦟䦛䦷䦶䲣䲟䲠䲡䱷䲢䴓\",6,\"䶮\",93],[\"8135f437\",\"\"]]");

/***/ }),
/* 251 */
/***/ (function(module) {

module.exports = JSON.parse("[[\"0\",\"\\u0000\",127],[\"a140\",\"　，、。．‧；：？！︰…‥﹐﹑﹒·﹔﹕﹖﹗｜–︱—︳╴︴﹏（）︵︶｛｝︷︸〔〕︹︺【】︻︼《》︽︾〈〉︿﹀「」﹁﹂『』﹃﹄﹙﹚\"],[\"a1a1\",\"﹛﹜﹝﹞‘’“”〝〞‵′＃＆＊※§〃○●△▲◎☆★◇◆□■▽▼㊣℅¯￣＿ˍ﹉﹊﹍﹎﹋﹌﹟﹠﹡＋－×÷±√＜＞＝≦≧≠∞≒≡﹢\",4,\"～∩∪⊥∠∟⊿㏒㏑∫∮∵∴♀♂⊕⊙↑↓←→↖↗↙↘∥∣／\"],[\"a240\",\"＼∕﹨＄￥〒￠￡％＠℃℉﹩﹪﹫㏕㎜㎝㎞㏎㎡㎎㎏㏄°兙兛兞兝兡兣嗧瓩糎▁\",7,\"▏▎▍▌▋▊▉┼┴┬┤├▔─│▕┌┐└┘╭\"],[\"a2a1\",\"╮╰╯═╞╪╡◢◣◥◤╱╲╳０\",9,\"Ⅰ\",9,\"〡\",8,\"十卄卅Ａ\",25,\"ａ\",21],[\"a340\",\"ｗｘｙｚΑ\",16,\"Σ\",6,\"α\",16,\"σ\",6,\"ㄅ\",10],[\"a3a1\",\"ㄐ\",25,\"˙ˉˊˇˋ\"],[\"a3e1\",\"€\"],[\"a440\",\"一乙丁七乃九了二人儿入八几刀刁力匕十卜又三下丈上丫丸凡久么也乞于亡兀刃勺千叉口土士夕大女子孑孓寸小尢尸山川工己已巳巾干廾弋弓才\"],[\"a4a1\",\"丑丐不中丰丹之尹予云井互五亢仁什仃仆仇仍今介仄元允內六兮公冗凶分切刈勻勾勿化匹午升卅卞厄友及反壬天夫太夭孔少尤尺屯巴幻廿弔引心戈戶手扎支文斗斤方日曰月木欠止歹毋比毛氏水火爪父爻片牙牛犬王丙\"],[\"a540\",\"世丕且丘主乍乏乎以付仔仕他仗代令仙仞充兄冉冊冬凹出凸刊加功包匆北匝仟半卉卡占卯卮去可古右召叮叩叨叼司叵叫另只史叱台句叭叻四囚外\"],[\"a5a1\",\"央失奴奶孕它尼巨巧左市布平幼弁弘弗必戊打扔扒扑斥旦朮本未末札正母民氐永汁汀氾犯玄玉瓜瓦甘生用甩田由甲申疋白皮皿目矛矢石示禾穴立丞丟乒乓乩亙交亦亥仿伉伙伊伕伍伐休伏仲件任仰仳份企伋光兇兆先全\"],[\"a640\",\"共再冰列刑划刎刖劣匈匡匠印危吉吏同吊吐吁吋各向名合吃后吆吒因回囝圳地在圭圬圯圩夙多夷夸妄奸妃好她如妁字存宇守宅安寺尖屹州帆并年\"],[\"a6a1\",\"式弛忙忖戎戌戍成扣扛托收早旨旬旭曲曳有朽朴朱朵次此死氖汝汗汙江池汐汕污汛汍汎灰牟牝百竹米糸缶羊羽老考而耒耳聿肉肋肌臣自至臼舌舛舟艮色艾虫血行衣西阡串亨位住佇佗佞伴佛何估佐佑伽伺伸佃佔似但佣\"],[\"a740\",\"作你伯低伶余佝佈佚兌克免兵冶冷別判利刪刨劫助努劬匣即卵吝吭吞吾否呎吧呆呃吳呈呂君吩告吹吻吸吮吵吶吠吼呀吱含吟听囪困囤囫坊坑址坍\"],[\"a7a1\",\"均坎圾坐坏圻壯夾妝妒妨妞妣妙妖妍妤妓妊妥孝孜孚孛完宋宏尬局屁尿尾岐岑岔岌巫希序庇床廷弄弟彤形彷役忘忌志忍忱快忸忪戒我抄抗抖技扶抉扭把扼找批扳抒扯折扮投抓抑抆改攻攸旱更束李杏材村杜杖杞杉杆杠\"],[\"a840\",\"杓杗步每求汞沙沁沈沉沅沛汪決沐汰沌汨沖沒汽沃汲汾汴沆汶沍沔沘沂灶灼災灸牢牡牠狄狂玖甬甫男甸皂盯矣私秀禿究系罕肖肓肝肘肛肚育良芒\"],[\"a8a1\",\"芋芍見角言谷豆豕貝赤走足身車辛辰迂迆迅迄巡邑邢邪邦那酉釆里防阮阱阪阬並乖乳事些亞享京佯依侍佳使佬供例來侃佰併侈佩佻侖佾侏侑佺兔兒兕兩具其典冽函刻券刷刺到刮制剁劾劻卒協卓卑卦卷卸卹取叔受味呵\"],[\"a940\",\"咖呸咕咀呻呷咄咒咆呼咐呱呶和咚呢周咋命咎固垃坷坪坩坡坦坤坼夜奉奇奈奄奔妾妻委妹妮姑姆姐姍始姓姊妯妳姒姅孟孤季宗定官宜宙宛尚屈居\"],[\"a9a1\",\"屆岷岡岸岩岫岱岳帘帚帖帕帛帑幸庚店府底庖延弦弧弩往征彿彼忝忠忽念忿怏怔怯怵怖怪怕怡性怩怫怛或戕房戾所承拉拌拄抿拂抹拒招披拓拔拋拈抨抽押拐拙拇拍抵拚抱拘拖拗拆抬拎放斧於旺昔易昌昆昂明昀昏昕昊\"],[\"aa40\",\"昇服朋杭枋枕東果杳杷枇枝林杯杰板枉松析杵枚枓杼杪杲欣武歧歿氓氛泣注泳沱泌泥河沽沾沼波沫法泓沸泄油況沮泗泅泱沿治泡泛泊沬泯泜泖泠\"],[\"aaa1\",\"炕炎炒炊炙爬爭爸版牧物狀狎狙狗狐玩玨玟玫玥甽疝疙疚的盂盲直知矽社祀祁秉秈空穹竺糾罔羌羋者肺肥肢肱股肫肩肴肪肯臥臾舍芳芝芙芭芽芟芹花芬芥芯芸芣芰芾芷虎虱初表軋迎返近邵邸邱邶采金長門阜陀阿阻附\"],[\"ab40\",\"陂隹雨青非亟亭亮信侵侯便俠俑俏保促侶俘俟俊俗侮俐俄係俚俎俞侷兗冒冑冠剎剃削前剌剋則勇勉勃勁匍南卻厚叛咬哀咨哎哉咸咦咳哇哂咽咪品\"],[\"aba1\",\"哄哈咯咫咱咻咩咧咿囿垂型垠垣垢城垮垓奕契奏奎奐姜姘姿姣姨娃姥姪姚姦威姻孩宣宦室客宥封屎屏屍屋峙峒巷帝帥帟幽庠度建弈弭彥很待徊律徇後徉怒思怠急怎怨恍恰恨恢恆恃恬恫恪恤扁拜挖按拼拭持拮拽指拱拷\"],[\"ac40\",\"拯括拾拴挑挂政故斫施既春昭映昧是星昨昱昤曷柿染柱柔某柬架枯柵柩柯柄柑枴柚查枸柏柞柳枰柙柢柝柒歪殃殆段毒毗氟泉洋洲洪流津洌洱洞洗\"],[\"aca1\",\"活洽派洶洛泵洹洧洸洩洮洵洎洫炫為炳炬炯炭炸炮炤爰牲牯牴狩狠狡玷珊玻玲珍珀玳甚甭畏界畎畋疫疤疥疢疣癸皆皇皈盈盆盃盅省盹相眉看盾盼眇矜砂研砌砍祆祉祈祇禹禺科秒秋穿突竿竽籽紂紅紀紉紇約紆缸美羿耄\"],[\"ad40\",\"耐耍耑耶胖胥胚胃胄背胡胛胎胞胤胝致舢苧范茅苣苛苦茄若茂茉苒苗英茁苜苔苑苞苓苟苯茆虐虹虻虺衍衫要觔計訂訃貞負赴赳趴軍軌述迦迢迪迥\"],[\"ada1\",\"迭迫迤迨郊郎郁郃酋酊重閂限陋陌降面革韋韭音頁風飛食首香乘亳倌倍倣俯倦倥俸倩倖倆值借倚倒們俺倀倔倨俱倡個候倘俳修倭倪俾倫倉兼冤冥冢凍凌准凋剖剜剔剛剝匪卿原厝叟哨唐唁唷哼哥哲唆哺唔哩哭員唉哮哪\"],[\"ae40\",\"哦唧唇哽唏圃圄埂埔埋埃堉夏套奘奚娑娘娜娟娛娓姬娠娣娩娥娌娉孫屘宰害家宴宮宵容宸射屑展屐峭峽峻峪峨峰島崁峴差席師庫庭座弱徒徑徐恙\"],[\"aea1\",\"恣恥恐恕恭恩息悄悟悚悍悔悌悅悖扇拳挈拿捎挾振捕捂捆捏捉挺捐挽挪挫挨捍捌效敉料旁旅時晉晏晃晒晌晅晁書朔朕朗校核案框桓根桂桔栩梳栗桌桑栽柴桐桀格桃株桅栓栘桁殊殉殷氣氧氨氦氤泰浪涕消涇浦浸海浙涓\"],[\"af40\",\"浬涉浮浚浴浩涌涊浹涅浥涔烊烘烤烙烈烏爹特狼狹狽狸狷玆班琉珮珠珪珞畔畝畜畚留疾病症疲疳疽疼疹痂疸皋皰益盍盎眩真眠眨矩砰砧砸砝破砷\"],[\"afa1\",\"砥砭砠砟砲祕祐祠祟祖神祝祗祚秤秣秧租秦秩秘窄窈站笆笑粉紡紗紋紊素索純紐紕級紜納紙紛缺罟羔翅翁耆耘耕耙耗耽耿胱脂胰脅胭胴脆胸胳脈能脊胼胯臭臬舀舐航舫舨般芻茫荒荔荊茸荐草茵茴荏茲茹茶茗荀茱茨荃\"],[\"b040\",\"虔蚊蚪蚓蚤蚩蚌蚣蚜衰衷袁袂衽衹記訐討訌訕訊託訓訖訏訑豈豺豹財貢起躬軒軔軏辱送逆迷退迺迴逃追逅迸邕郡郝郢酒配酌釘針釗釜釙閃院陣陡\"],[\"b0a1\",\"陛陝除陘陞隻飢馬骨高鬥鬲鬼乾偺偽停假偃偌做偉健偶偎偕偵側偷偏倏偯偭兜冕凰剪副勒務勘動匐匏匙匿區匾參曼商啪啦啄啞啡啃啊唱啖問啕唯啤唸售啜唬啣唳啁啗圈國圉域堅堊堆埠埤基堂堵執培夠奢娶婁婉婦婪婀\"],[\"b140\",\"娼婢婚婆婊孰寇寅寄寂宿密尉專將屠屜屝崇崆崎崛崖崢崑崩崔崙崤崧崗巢常帶帳帷康庸庶庵庾張強彗彬彩彫得徙從徘御徠徜恿患悉悠您惋悴惦悽\"],[\"b1a1\",\"情悻悵惜悼惘惕惆惟悸惚惇戚戛扈掠控捲掖探接捷捧掘措捱掩掉掃掛捫推掄授掙採掬排掏掀捻捩捨捺敝敖救教敗啟敏敘敕敔斜斛斬族旋旌旎晝晚晤晨晦晞曹勗望梁梯梢梓梵桿桶梱梧梗械梃棄梭梆梅梔條梨梟梡梂欲殺\"],[\"b240\",\"毫毬氫涎涼淳淙液淡淌淤添淺清淇淋涯淑涮淞淹涸混淵淅淒渚涵淚淫淘淪深淮淨淆淄涪淬涿淦烹焉焊烽烯爽牽犁猜猛猖猓猙率琅琊球理現琍瓠瓶\"],[\"b2a1\",\"瓷甜產略畦畢異疏痔痕疵痊痍皎盔盒盛眷眾眼眶眸眺硫硃硎祥票祭移窒窕笠笨笛第符笙笞笮粒粗粕絆絃統紮紹紼絀細紳組累終紲紱缽羞羚翌翎習耜聊聆脯脖脣脫脩脰脤舂舵舷舶船莎莞莘荸莢莖莽莫莒莊莓莉莠荷荻荼\"],[\"b340\",\"莆莧處彪蛇蛀蚶蛄蚵蛆蛋蚱蚯蛉術袞袈被袒袖袍袋覓規訪訝訣訥許設訟訛訢豉豚販責貫貨貪貧赧赦趾趺軛軟這逍通逗連速逝逐逕逞造透逢逖逛途\"],[\"b3a1\",\"部郭都酗野釵釦釣釧釭釩閉陪陵陳陸陰陴陶陷陬雀雪雩章竟頂頃魚鳥鹵鹿麥麻傢傍傅備傑傀傖傘傚最凱割剴創剩勞勝勛博厥啻喀喧啼喊喝喘喂喜喪喔喇喋喃喳單喟唾喲喚喻喬喱啾喉喫喙圍堯堪場堤堰報堡堝堠壹壺奠\"],[\"b440\",\"婷媚婿媒媛媧孳孱寒富寓寐尊尋就嵌嵐崴嵇巽幅帽幀幃幾廊廁廂廄弼彭復循徨惑惡悲悶惠愜愣惺愕惰惻惴慨惱愎惶愉愀愒戟扉掣掌描揀揩揉揆揍\"],[\"b4a1\",\"插揣提握揖揭揮捶援揪換摒揚揹敞敦敢散斑斐斯普晰晴晶景暑智晾晷曾替期朝棺棕棠棘棗椅棟棵森棧棹棒棲棣棋棍植椒椎棉棚楮棻款欺欽殘殖殼毯氮氯氬港游湔渡渲湧湊渠渥渣減湛湘渤湖湮渭渦湯渴湍渺測湃渝渾滋\"],[\"b540\",\"溉渙湎湣湄湲湩湟焙焚焦焰無然煮焜牌犄犀猶猥猴猩琺琪琳琢琥琵琶琴琯琛琦琨甥甦畫番痢痛痣痙痘痞痠登發皖皓皴盜睏短硝硬硯稍稈程稅稀窘\"],[\"b5a1\",\"窗窖童竣等策筆筐筒答筍筋筏筑粟粥絞結絨絕紫絮絲絡給絢絰絳善翔翕耋聒肅腕腔腋腑腎脹腆脾腌腓腴舒舜菩萃菸萍菠菅萋菁華菱菴著萊菰萌菌菽菲菊萸萎萄菜萇菔菟虛蛟蛙蛭蛔蛛蛤蛐蛞街裁裂袱覃視註詠評詞証詁\"],[\"b640\",\"詔詛詐詆訴診訶詖象貂貯貼貳貽賁費賀貴買貶貿貸越超趁跎距跋跚跑跌跛跆軻軸軼辜逮逵週逸進逶鄂郵鄉郾酣酥量鈔鈕鈣鈉鈞鈍鈐鈇鈑閔閏開閑\"],[\"b6a1\",\"間閒閎隊階隋陽隅隆隍陲隄雁雅雄集雇雯雲韌項順須飧飪飯飩飲飭馮馭黃黍黑亂傭債傲傳僅傾催傷傻傯僇剿剷剽募勦勤勢勣匯嗟嗨嗓嗦嗎嗜嗇嗑嗣嗤嗯嗚嗡嗅嗆嗥嗉園圓塞塑塘塗塚塔填塌塭塊塢塒塋奧嫁嫉嫌媾媽媼\"],[\"b740\",\"媳嫂媲嵩嵯幌幹廉廈弒彙徬微愚意慈感想愛惹愁愈慎慌慄慍愾愴愧愍愆愷戡戢搓搾搞搪搭搽搬搏搜搔損搶搖搗搆敬斟新暗暉暇暈暖暄暘暍會榔業\"],[\"b7a1\",\"楚楷楠楔極椰概楊楨楫楞楓楹榆楝楣楛歇歲毀殿毓毽溢溯滓溶滂源溝滇滅溥溘溼溺溫滑準溜滄滔溪溧溴煎煙煩煤煉照煜煬煦煌煥煞煆煨煖爺牒猷獅猿猾瑯瑚瑕瑟瑞瑁琿瑙瑛瑜當畸瘀痰瘁痲痱痺痿痴痳盞盟睛睫睦睞督\"],[\"b840\",\"睹睪睬睜睥睨睢矮碎碰碗碘碌碉硼碑碓硿祺祿禁萬禽稜稚稠稔稟稞窟窠筷節筠筮筧粱粳粵經絹綑綁綏絛置罩罪署義羨群聖聘肆肄腱腰腸腥腮腳腫\"],[\"b8a1\",\"腹腺腦舅艇蒂葷落萱葵葦葫葉葬葛萼萵葡董葩葭葆虞虜號蛹蜓蜈蜇蜀蛾蛻蜂蜃蜆蜊衙裟裔裙補裘裝裡裊裕裒覜解詫該詳試詩詰誇詼詣誠話誅詭詢詮詬詹詻訾詨豢貊貉賊資賈賄貲賃賂賅跡跟跨路跳跺跪跤跦躲較載軾輊\"],[\"b940\",\"辟農運遊道遂達逼違遐遇遏過遍遑逾遁鄒鄗酬酪酩釉鈷鉗鈸鈽鉀鈾鉛鉋鉤鉑鈴鉉鉍鉅鈹鈿鉚閘隘隔隕雍雋雉雊雷電雹零靖靴靶預頑頓頊頒頌飼飴\"],[\"b9a1\",\"飽飾馳馱馴髡鳩麂鼎鼓鼠僧僮僥僖僭僚僕像僑僱僎僩兢凳劃劂匱厭嗾嘀嘛嘗嗽嘔嘆嘉嘍嘎嗷嘖嘟嘈嘐嗶團圖塵塾境墓墊塹墅塽壽夥夢夤奪奩嫡嫦嫩嫗嫖嫘嫣孵寞寧寡寥實寨寢寤察對屢嶄嶇幛幣幕幗幔廓廖弊彆彰徹慇\"],[\"ba40\",\"愿態慷慢慣慟慚慘慵截撇摘摔撤摸摟摺摑摧搴摭摻敲斡旗旖暢暨暝榜榨榕槁榮槓構榛榷榻榫榴槐槍榭槌榦槃榣歉歌氳漳演滾漓滴漩漾漠漬漏漂漢\"],[\"baa1\",\"滿滯漆漱漸漲漣漕漫漯澈漪滬漁滲滌滷熔熙煽熊熄熒爾犒犖獄獐瑤瑣瑪瑰瑭甄疑瘧瘍瘋瘉瘓盡監瞄睽睿睡磁碟碧碳碩碣禎福禍種稱窪窩竭端管箕箋筵算箝箔箏箸箇箄粹粽精綻綰綜綽綾綠緊綴網綱綺綢綿綵綸維緒緇綬\"],[\"bb40\",\"罰翠翡翟聞聚肇腐膀膏膈膊腿膂臧臺與舔舞艋蓉蒿蓆蓄蒙蒞蒲蒜蓋蒸蓀蓓蒐蒼蓑蓊蜿蜜蜻蜢蜥蜴蜘蝕蜷蜩裳褂裴裹裸製裨褚裯誦誌語誣認誡誓誤\"],[\"bba1\",\"說誥誨誘誑誚誧豪貍貌賓賑賒赫趙趕跼輔輒輕輓辣遠遘遜遣遙遞遢遝遛鄙鄘鄞酵酸酷酴鉸銀銅銘銖鉻銓銜銨鉼銑閡閨閩閣閥閤隙障際雌雒需靼鞅韶頗領颯颱餃餅餌餉駁骯骰髦魁魂鳴鳶鳳麼鼻齊億儀僻僵價儂儈儉儅凜\"],[\"bc40\",\"劇劈劉劍劊勰厲嘮嘻嘹嘲嘿嘴嘩噓噎噗噴嘶嘯嘰墀墟增墳墜墮墩墦奭嬉嫻嬋嫵嬌嬈寮寬審寫層履嶝嶔幢幟幡廢廚廟廝廣廠彈影德徵慶慧慮慝慕憂\"],[\"bca1\",\"慼慰慫慾憧憐憫憎憬憚憤憔憮戮摩摯摹撞撲撈撐撰撥撓撕撩撒撮播撫撚撬撙撢撳敵敷數暮暫暴暱樣樟槨樁樞標槽模樓樊槳樂樅槭樑歐歎殤毅毆漿潼澄潑潦潔澆潭潛潸潮澎潺潰潤澗潘滕潯潠潟熟熬熱熨牖犛獎獗瑩璋璃\"],[\"bd40\",\"瑾璀畿瘠瘩瘟瘤瘦瘡瘢皚皺盤瞎瞇瞌瞑瞋磋磅確磊碾磕碼磐稿稼穀稽稷稻窯窮箭箱範箴篆篇篁箠篌糊締練緯緻緘緬緝編緣線緞緩綞緙緲緹罵罷羯\"],[\"bda1\",\"翩耦膛膜膝膠膚膘蔗蔽蔚蓮蔬蔭蔓蔑蔣蔡蔔蓬蔥蓿蔆螂蝴蝶蝠蝦蝸蝨蝙蝗蝌蝓衛衝褐複褒褓褕褊誼諒談諄誕請諸課諉諂調誰論諍誶誹諛豌豎豬賠賞賦賤賬賭賢賣賜質賡赭趟趣踫踐踝踢踏踩踟踡踞躺輝輛輟輩輦輪輜輞\"],[\"be40\",\"輥適遮遨遭遷鄰鄭鄧鄱醇醉醋醃鋅銻銷鋪銬鋤鋁銳銼鋒鋇鋰銲閭閱霄霆震霉靠鞍鞋鞏頡頫頜颳養餓餒餘駝駐駟駛駑駕駒駙骷髮髯鬧魅魄魷魯鴆鴉\"],[\"bea1\",\"鴃麩麾黎墨齒儒儘儔儐儕冀冪凝劑劓勳噙噫噹噩噤噸噪器噥噱噯噬噢噶壁墾壇壅奮嬝嬴學寰導彊憲憑憩憊懍憶憾懊懈戰擅擁擋撻撼據擄擇擂操撿擒擔撾整曆曉暹曄曇暸樽樸樺橙橫橘樹橄橢橡橋橇樵機橈歙歷氅濂澱澡\"],[\"bf40\",\"濃澤濁澧澳激澹澶澦澠澴熾燉燐燒燈燕熹燎燙燜燃燄獨璜璣璘璟璞瓢甌甍瘴瘸瘺盧盥瞠瞞瞟瞥磨磚磬磧禦積穎穆穌穋窺篙簑築篤篛篡篩篦糕糖縊\"],[\"bfa1\",\"縑縈縛縣縞縝縉縐罹羲翰翱翮耨膳膩膨臻興艘艙蕊蕙蕈蕨蕩蕃蕉蕭蕪蕞螃螟螞螢融衡褪褲褥褫褡親覦諦諺諫諱謀諜諧諮諾謁謂諷諭諳諶諼豫豭貓賴蹄踱踴蹂踹踵輻輯輸輳辨辦遵遴選遲遼遺鄴醒錠錶鋸錳錯錢鋼錫錄錚\"],[\"c040\",\"錐錦錡錕錮錙閻隧隨險雕霎霑霖霍霓霏靛靜靦鞘頰頸頻頷頭頹頤餐館餞餛餡餚駭駢駱骸骼髻髭鬨鮑鴕鴣鴦鴨鴒鴛默黔龍龜優償儡儲勵嚎嚀嚐嚅嚇\"],[\"c0a1\",\"嚏壕壓壑壎嬰嬪嬤孺尷屨嶼嶺嶽嶸幫彌徽應懂懇懦懋戲戴擎擊擘擠擰擦擬擱擢擭斂斃曙曖檀檔檄檢檜櫛檣橾檗檐檠歜殮毚氈濘濱濟濠濛濤濫濯澀濬濡濩濕濮濰燧營燮燦燥燭燬燴燠爵牆獰獲璩環璦璨癆療癌盪瞳瞪瞰瞬\"],[\"c140\",\"瞧瞭矯磷磺磴磯礁禧禪穗窿簇簍篾篷簌篠糠糜糞糢糟糙糝縮績繆縷縲繃縫總縱繅繁縴縹繈縵縿縯罄翳翼聱聲聰聯聳臆臃膺臂臀膿膽臉膾臨舉艱薪\"],[\"c1a1\",\"薄蕾薜薑薔薯薛薇薨薊虧蟀蟑螳蟒蟆螫螻螺蟈蟋褻褶襄褸褽覬謎謗謙講謊謠謝謄謐豁谿豳賺賽購賸賻趨蹉蹋蹈蹊轄輾轂轅輿避遽還邁邂邀鄹醣醞醜鍍鎂錨鍵鍊鍥鍋錘鍾鍬鍛鍰鍚鍔闊闋闌闈闆隱隸雖霜霞鞠韓顆颶餵騁\"],[\"c240\",\"駿鮮鮫鮪鮭鴻鴿麋黏點黜黝黛鼾齋叢嚕嚮壙壘嬸彝懣戳擴擲擾攆擺擻擷斷曜朦檳檬櫃檻檸櫂檮檯歟歸殯瀉瀋濾瀆濺瀑瀏燻燼燾燸獷獵璧璿甕癖癘\"],[\"c2a1\",\"癒瞽瞿瞻瞼礎禮穡穢穠竄竅簫簧簪簞簣簡糧織繕繞繚繡繒繙罈翹翻職聶臍臏舊藏薩藍藐藉薰薺薹薦蟯蟬蟲蟠覆覲觴謨謹謬謫豐贅蹙蹣蹦蹤蹟蹕軀轉轍邇邃邈醫醬釐鎔鎊鎖鎢鎳鎮鎬鎰鎘鎚鎗闔闖闐闕離雜雙雛雞霤鞣鞦\"],[\"c340\",\"鞭韹額顏題顎顓颺餾餿餽餮馥騎髁鬃鬆魏魎魍鯊鯉鯽鯈鯀鵑鵝鵠黠鼕鼬儳嚥壞壟壢寵龐廬懲懷懶懵攀攏曠曝櫥櫝櫚櫓瀛瀟瀨瀚瀝瀕瀘爆爍牘犢獸\"],[\"c3a1\",\"獺璽瓊瓣疇疆癟癡矇礙禱穫穩簾簿簸簽簷籀繫繭繹繩繪羅繳羶羹羸臘藩藝藪藕藤藥藷蟻蠅蠍蟹蟾襠襟襖襞譁譜識證譚譎譏譆譙贈贊蹼蹲躇蹶蹬蹺蹴轔轎辭邊邋醱醮鏡鏑鏟鏃鏈鏜鏝鏖鏢鏍鏘鏤鏗鏨關隴難霪霧靡韜韻類\"],[\"c440\",\"願顛颼饅饉騖騙鬍鯨鯧鯖鯛鶉鵡鵲鵪鵬麒麗麓麴勸嚨嚷嚶嚴嚼壤孀孃孽寶巉懸懺攘攔攙曦朧櫬瀾瀰瀲爐獻瓏癢癥礦礪礬礫竇競籌籃籍糯糰辮繽繼\"],[\"c4a1\",\"纂罌耀臚艦藻藹蘑藺蘆蘋蘇蘊蠔蠕襤覺觸議譬警譯譟譫贏贍躉躁躅躂醴釋鐘鐃鏽闡霰飄饒饑馨騫騰騷騵鰓鰍鹹麵黨鼯齟齣齡儷儸囁囀囂夔屬巍懼懾攝攜斕曩櫻欄櫺殲灌爛犧瓖瓔癩矓籐纏續羼蘗蘭蘚蠣蠢蠡蠟襪襬覽譴\"],[\"c540\",\"護譽贓躊躍躋轟辯醺鐮鐳鐵鐺鐸鐲鐫闢霸霹露響顧顥饗驅驃驀騾髏魔魑鰭鰥鶯鶴鷂鶸麝黯鼙齜齦齧儼儻囈囊囉孿巔巒彎懿攤權歡灑灘玀瓤疊癮癬\"],[\"c5a1\",\"禳籠籟聾聽臟襲襯觼讀贖贗躑躓轡酈鑄鑑鑒霽霾韃韁顫饕驕驍髒鬚鱉鰱鰾鰻鷓鷗鼴齬齪龔囌巖戀攣攫攪曬欐瓚竊籤籣籥纓纖纔臢蘸蘿蠱變邐邏鑣鑠鑤靨顯饜驚驛驗髓體髑鱔鱗鱖鷥麟黴囑壩攬灞癱癲矗罐羈蠶蠹衢讓讒\"],[\"c640\",\"讖艷贛釀鑪靂靈靄韆顰驟鬢魘鱟鷹鷺鹼鹽鼇齷齲廳欖灣籬籮蠻觀躡釁鑲鑰顱饞髖鬣黌灤矚讚鑷韉驢驥纜讜躪釅鑽鑾鑼鱷鱸黷豔鑿鸚爨驪鬱鸛鸞籲\"],[\"c940\",\"乂乜凵匚厂万丌乇亍囗兀屮彳丏冇与丮亓仂仉仈冘勼卬厹圠夃夬尐巿旡殳毌气爿丱丼仨仜仩仡仝仚刌匜卌圢圣夗夯宁宄尒尻屴屳帄庀庂忉戉扐氕\"],[\"c9a1\",\"氶汃氿氻犮犰玊禸肊阞伎优伬仵伔仱伀价伈伝伂伅伢伓伄仴伒冱刓刉刐劦匢匟卍厊吇囡囟圮圪圴夼妀奼妅奻奾奷奿孖尕尥屼屺屻屾巟幵庄异弚彴忕忔忏扜扞扤扡扦扢扙扠扚扥旯旮朾朹朸朻机朿朼朳氘汆汒汜汏汊汔汋\"],[\"ca40\",\"汌灱牞犴犵玎甪癿穵网艸艼芀艽艿虍襾邙邗邘邛邔阢阤阠阣佖伻佢佉体佤伾佧佒佟佁佘伭伳伿佡冏冹刜刞刡劭劮匉卣卲厎厏吰吷吪呔呅吙吜吥吘\"],[\"caa1\",\"吽呏呁吨吤呇囮囧囥坁坅坌坉坋坒夆奀妦妘妠妗妎妢妐妏妧妡宎宒尨尪岍岏岈岋岉岒岊岆岓岕巠帊帎庋庉庌庈庍弅弝彸彶忒忑忐忭忨忮忳忡忤忣忺忯忷忻怀忴戺抃抌抎抏抔抇扱扻扺扰抁抈扷扽扲扴攷旰旴旳旲旵杅杇\"],[\"cb40\",\"杙杕杌杈杝杍杚杋毐氙氚汸汧汫沄沋沏汱汯汩沚汭沇沕沜汦汳汥汻沎灴灺牣犿犽狃狆狁犺狅玕玗玓玔玒町甹疔疕皁礽耴肕肙肐肒肜芐芏芅芎芑芓\"],[\"cba1\",\"芊芃芄豸迉辿邟邡邥邞邧邠阰阨阯阭丳侘佼侅佽侀侇佶佴侉侄佷佌侗佪侚佹侁佸侐侜侔侞侒侂侕佫佮冞冼冾刵刲刳剆刱劼匊匋匼厒厔咇呿咁咑咂咈呫呺呾呥呬呴呦咍呯呡呠咘呣呧呤囷囹坯坲坭坫坱坰坶垀坵坻坳坴坢\"],[\"cc40\",\"坨坽夌奅妵妺姏姎妲姌姁妶妼姃姖妱妽姀姈妴姇孢孥宓宕屄屇岮岤岠岵岯岨岬岟岣岭岢岪岧岝岥岶岰岦帗帔帙弨弢弣弤彔徂彾彽忞忥怭怦怙怲怋\"],[\"cca1\",\"怴怊怗怳怚怞怬怢怍怐怮怓怑怌怉怜戔戽抭抴拑抾抪抶拊抮抳抯抻抩抰抸攽斨斻昉旼昄昒昈旻昃昋昍昅旽昑昐曶朊枅杬枎枒杶杻枘枆构杴枍枌杺枟枑枙枃杽极杸杹枔欥殀歾毞氝沓泬泫泮泙沶泔沭泧沷泐泂沺泃泆泭泲\"],[\"cd40\",\"泒泝沴沊沝沀泞泀洰泍泇沰泹泏泩泑炔炘炅炓炆炄炑炖炂炚炃牪狖狋狘狉狜狒狔狚狌狑玤玡玭玦玢玠玬玝瓝瓨甿畀甾疌疘皯盳盱盰盵矸矼矹矻矺\"],[\"cda1\",\"矷祂礿秅穸穻竻籵糽耵肏肮肣肸肵肭舠芠苀芫芚芘芛芵芧芮芼芞芺芴芨芡芩苂芤苃芶芢虰虯虭虮豖迒迋迓迍迖迕迗邲邴邯邳邰阹阽阼阺陃俍俅俓侲俉俋俁俔俜俙侻侳俛俇俖侺俀侹俬剄剉勀勂匽卼厗厖厙厘咺咡咭咥哏\"],[\"ce40\",\"哃茍咷咮哖咶哅哆咠呰咼咢咾呲哞咰垵垞垟垤垌垗垝垛垔垘垏垙垥垚垕壴复奓姡姞姮娀姱姝姺姽姼姶姤姲姷姛姩姳姵姠姾姴姭宨屌峐峘峌峗峋峛\"],[\"cea1\",\"峞峚峉峇峊峖峓峔峏峈峆峎峟峸巹帡帢帣帠帤庰庤庢庛庣庥弇弮彖徆怷怹恔恲恞恅恓恇恉恛恌恀恂恟怤恄恘恦恮扂扃拏挍挋拵挎挃拫拹挏挌拸拶挀挓挔拺挕拻拰敁敃斪斿昶昡昲昵昜昦昢昳昫昺昝昴昹昮朏朐柁柲柈枺\"],[\"cf40\",\"柜枻柸柘柀枷柅柫柤柟枵柍枳柷柶柮柣柂枹柎柧柰枲柼柆柭柌枮柦柛柺柉柊柃柪柋欨殂殄殶毖毘毠氠氡洨洴洭洟洼洿洒洊泚洳洄洙洺洚洑洀洝浂\"],[\"cfa1\",\"洁洘洷洃洏浀洇洠洬洈洢洉洐炷炟炾炱炰炡炴炵炩牁牉牊牬牰牳牮狊狤狨狫狟狪狦狣玅珌珂珈珅玹玶玵玴珫玿珇玾珃珆玸珋瓬瓮甮畇畈疧疪癹盄眈眃眄眅眊盷盻盺矧矨砆砑砒砅砐砏砎砉砃砓祊祌祋祅祄秕种秏秖秎窀\"],[\"d040\",\"穾竑笀笁籺籸籹籿粀粁紃紈紁罘羑羍羾耇耎耏耔耷胘胇胠胑胈胂胐胅胣胙胜胊胕胉胏胗胦胍臿舡芔苙苾苹茇苨茀苕茺苫苖苴苬苡苲苵茌苻苶苰苪\"],[\"d0a1\",\"苤苠苺苳苭虷虴虼虳衁衎衧衪衩觓訄訇赲迣迡迮迠郱邽邿郕郅邾郇郋郈釔釓陔陏陑陓陊陎倞倅倇倓倢倰倛俵俴倳倷倬俶俷倗倜倠倧倵倯倱倎党冔冓凊凄凅凈凎剡剚剒剞剟剕剢勍匎厞唦哢唗唒哧哳哤唚哿唄唈哫唑唅哱\"],[\"d140\",\"唊哻哷哸哠唎唃唋圁圂埌堲埕埒垺埆垽垼垸垶垿埇埐垹埁夎奊娙娖娭娮娕娏娗娊娞娳孬宧宭宬尃屖屔峬峿峮峱峷崀峹帩帨庨庮庪庬弳弰彧恝恚恧\"],[\"d1a1\",\"恁悢悈悀悒悁悝悃悕悛悗悇悜悎戙扆拲挐捖挬捄捅挶捃揤挹捋捊挼挩捁挴捘捔捙挭捇挳捚捑挸捗捀捈敊敆旆旃旄旂晊晟晇晑朒朓栟栚桉栲栳栻桋桏栖栱栜栵栫栭栯桎桄栴栝栒栔栦栨栮桍栺栥栠欬欯欭欱欴歭肂殈毦毤\"],[\"d240\",\"毨毣毢毧氥浺浣浤浶洍浡涒浘浢浭浯涑涍淯浿涆浞浧浠涗浰浼浟涂涘洯浨涋浾涀涄洖涃浻浽浵涐烜烓烑烝烋缹烢烗烒烞烠烔烍烅烆烇烚烎烡牂牸\"],[\"d2a1\",\"牷牶猀狺狴狾狶狳狻猁珓珙珥珖玼珧珣珩珜珒珛珔珝珚珗珘珨瓞瓟瓴瓵甡畛畟疰痁疻痄痀疿疶疺皊盉眝眛眐眓眒眣眑眕眙眚眢眧砣砬砢砵砯砨砮砫砡砩砳砪砱祔祛祏祜祓祒祑秫秬秠秮秭秪秜秞秝窆窉窅窋窌窊窇竘笐\"],[\"d340\",\"笄笓笅笏笈笊笎笉笒粄粑粊粌粈粍粅紞紝紑紎紘紖紓紟紒紏紌罜罡罞罠罝罛羖羒翃翂翀耖耾耹胺胲胹胵脁胻脀舁舯舥茳茭荄茙荑茥荖茿荁茦茜茢\"],[\"d3a1\",\"荂荎茛茪茈茼荍茖茤茠茷茯茩荇荅荌荓茞茬荋茧荈虓虒蚢蚨蚖蚍蚑蚞蚇蚗蚆蚋蚚蚅蚥蚙蚡蚧蚕蚘蚎蚝蚐蚔衃衄衭衵衶衲袀衱衿衯袃衾衴衼訒豇豗豻貤貣赶赸趵趷趶軑軓迾迵适迿迻逄迼迶郖郠郙郚郣郟郥郘郛郗郜郤酐\"],[\"d440\",\"酎酏釕釢釚陜陟隼飣髟鬯乿偰偪偡偞偠偓偋偝偲偈偍偁偛偊偢倕偅偟偩偫偣偤偆偀偮偳偗偑凐剫剭剬剮勖勓匭厜啵啶唼啍啐唴唪啑啢唶唵唰啒啅\"],[\"d4a1\",\"唌唲啥啎唹啈唭唻啀啋圊圇埻堔埢埶埜埴堀埭埽堈埸堋埳埏堇埮埣埲埥埬埡堎埼堐埧堁堌埱埩埰堍堄奜婠婘婕婧婞娸娵婭婐婟婥婬婓婤婗婃婝婒婄婛婈媎娾婍娹婌婰婩婇婑婖婂婜孲孮寁寀屙崞崋崝崚崠崌崨崍崦崥崏\"],[\"d540\",\"崰崒崣崟崮帾帴庱庴庹庲庳弶弸徛徖徟悊悐悆悾悰悺惓惔惏惤惙惝惈悱惛悷惊悿惃惍惀挲捥掊掂捽掽掞掭掝掗掫掎捯掇掐据掯捵掜捭掮捼掤挻掟\"],[\"d5a1\",\"捸掅掁掑掍捰敓旍晥晡晛晙晜晢朘桹梇梐梜桭桮梮梫楖桯梣梬梩桵桴梲梏桷梒桼桫桲梪梀桱桾梛梖梋梠梉梤桸桻梑梌梊桽欶欳欷欸殑殏殍殎殌氪淀涫涴涳湴涬淩淢涷淶淔渀淈淠淟淖涾淥淜淝淛淴淊涽淭淰涺淕淂淏淉\"],[\"d640\",\"淐淲淓淽淗淍淣涻烺焍烷焗烴焌烰焄烳焐烼烿焆焓焀烸烶焋焂焎牾牻牼牿猝猗猇猑猘猊猈狿猏猞玈珶珸珵琄琁珽琇琀珺珼珿琌琋珴琈畤畣痎痒痏\"],[\"d6a1\",\"痋痌痑痐皏皉盓眹眯眭眱眲眴眳眽眥眻眵硈硒硉硍硊硌砦硅硐祤祧祩祪祣祫祡离秺秸秶秷窏窔窐笵筇笴笥笰笢笤笳笘笪笝笱笫笭笯笲笸笚笣粔粘粖粣紵紽紸紶紺絅紬紩絁絇紾紿絊紻紨罣羕羜羝羛翊翋翍翐翑翇翏翉耟\"],[\"d740\",\"耞耛聇聃聈脘脥脙脛脭脟脬脞脡脕脧脝脢舑舸舳舺舴舲艴莐莣莨莍荺荳莤荴莏莁莕莙荵莔莩荽莃莌莝莛莪莋荾莥莯莈莗莰荿莦莇莮荶莚虙虖蚿蚷\"],[\"d7a1\",\"蛂蛁蛅蚺蚰蛈蚹蚳蚸蛌蚴蚻蚼蛃蚽蚾衒袉袕袨袢袪袚袑袡袟袘袧袙袛袗袤袬袌袓袎覂觖觙觕訰訧訬訞谹谻豜豝豽貥赽赻赹趼跂趹趿跁軘軞軝軜軗軠軡逤逋逑逜逌逡郯郪郰郴郲郳郔郫郬郩酖酘酚酓酕釬釴釱釳釸釤釹釪\"],[\"d840\",\"釫釷釨釮镺閆閈陼陭陫陱陯隿靪頄飥馗傛傕傔傞傋傣傃傌傎傝偨傜傒傂傇兟凔匒匑厤厧喑喨喥喭啷噅喢喓喈喏喵喁喣喒喤啽喌喦啿喕喡喎圌堩堷\"],[\"d8a1\",\"堙堞堧堣堨埵塈堥堜堛堳堿堶堮堹堸堭堬堻奡媯媔媟婺媢媞婸媦婼媥媬媕媮娷媄媊媗媃媋媩婻婽媌媜媏媓媝寪寍寋寔寑寊寎尌尰崷嵃嵫嵁嵋崿崵嵑嵎嵕崳崺嵒崽崱嵙嵂崹嵉崸崼崲崶嵀嵅幄幁彘徦徥徫惉悹惌惢惎惄愔\"],[\"d940\",\"惲愊愖愅惵愓惸惼惾惁愃愘愝愐惿愄愋扊掔掱掰揎揥揨揯揃撝揳揊揠揶揕揲揵摡揟掾揝揜揄揘揓揂揇揌揋揈揰揗揙攲敧敪敤敜敨敥斌斝斞斮旐旒\"],[\"d9a1\",\"晼晬晻暀晱晹晪晲朁椌棓椄棜椪棬棪棱椏棖棷棫棤棶椓椐棳棡椇棌椈楰梴椑棯棆椔棸棐棽棼棨椋椊椗棎棈棝棞棦棴棑椆棔棩椕椥棇欹欻欿欼殔殗殙殕殽毰毲毳氰淼湆湇渟湉溈渼渽湅湢渫渿湁湝湳渜渳湋湀湑渻渃渮湞\"],[\"da40\",\"湨湜湡渱渨湠湱湫渹渢渰湓湥渧湸湤湷湕湹湒湦渵渶湚焠焞焯烻焮焱焣焥焢焲焟焨焺焛牋牚犈犉犆犅犋猒猋猰猢猱猳猧猲猭猦猣猵猌琮琬琰琫琖\"],[\"daa1\",\"琚琡琭琱琤琣琝琩琠琲瓻甯畯畬痧痚痡痦痝痟痤痗皕皒盚睆睇睄睍睅睊睎睋睌矞矬硠硤硥硜硭硱硪确硰硩硨硞硢祴祳祲祰稂稊稃稌稄窙竦竤筊笻筄筈筌筎筀筘筅粢粞粨粡絘絯絣絓絖絧絪絏絭絜絫絒絔絩絑絟絎缾缿罥\"],[\"db40\",\"罦羢羠羡翗聑聏聐胾胔腃腊腒腏腇脽腍脺臦臮臷臸臹舄舼舽舿艵茻菏菹萣菀菨萒菧菤菼菶萐菆菈菫菣莿萁菝菥菘菿菡菋菎菖菵菉萉萏菞萑萆菂菳\"],[\"dba1\",\"菕菺菇菑菪萓菃菬菮菄菻菗菢萛菛菾蛘蛢蛦蛓蛣蛚蛪蛝蛫蛜蛬蛩蛗蛨蛑衈衖衕袺裗袹袸裀袾袶袼袷袽袲褁裉覕覘覗觝觚觛詎詍訹詙詀詗詘詄詅詒詈詑詊詌詏豟貁貀貺貾貰貹貵趄趀趉跘跓跍跇跖跜跏跕跙跈跗跅軯軷軺\"],[\"dc40\",\"軹軦軮軥軵軧軨軶軫軱軬軴軩逭逴逯鄆鄬鄄郿郼鄈郹郻鄁鄀鄇鄅鄃酡酤酟酢酠鈁鈊鈥鈃鈚鈦鈏鈌鈀鈒釿釽鈆鈄鈧鈂鈜鈤鈙鈗鈅鈖镻閍閌閐隇陾隈\"],[\"dca1\",\"隉隃隀雂雈雃雱雰靬靰靮頇颩飫鳦黹亃亄亶傽傿僆傮僄僊傴僈僂傰僁傺傱僋僉傶傸凗剺剸剻剼嗃嗛嗌嗐嗋嗊嗝嗀嗔嗄嗩喿嗒喍嗏嗕嗢嗖嗈嗲嗍嗙嗂圔塓塨塤塏塍塉塯塕塎塝塙塥塛堽塣塱壼嫇嫄嫋媺媸媱媵媰媿嫈媻嫆\"],[\"dd40\",\"媷嫀嫊媴媶嫍媹媐寖寘寙尟尳嵱嵣嵊嵥嵲嵬嵞嵨嵧嵢巰幏幎幊幍幋廅廌廆廋廇彀徯徭惷慉慊愫慅愶愲愮慆愯慏愩慀戠酨戣戥戤揅揱揫搐搒搉搠搤\"],[\"dda1\",\"搳摃搟搕搘搹搷搢搣搌搦搰搨摁搵搯搊搚摀搥搧搋揧搛搮搡搎敯斒旓暆暌暕暐暋暊暙暔晸朠楦楟椸楎楢楱椿楅楪椹楂楗楙楺楈楉椵楬椳椽楥棰楸椴楩楀楯楄楶楘楁楴楌椻楋椷楜楏楑椲楒椯楻椼歆歅歃歂歈歁殛嗀毻毼\"],[\"de40\",\"毹毷毸溛滖滈溏滀溟溓溔溠溱溹滆滒溽滁溞滉溷溰滍溦滏溲溾滃滜滘溙溒溎溍溤溡溿溳滐滊溗溮溣煇煔煒煣煠煁煝煢煲煸煪煡煂煘煃煋煰煟煐煓\"],[\"dea1\",\"煄煍煚牏犍犌犑犐犎猼獂猻猺獀獊獉瑄瑊瑋瑒瑑瑗瑀瑏瑐瑎瑂瑆瑍瑔瓡瓿瓾瓽甝畹畷榃痯瘏瘃痷痾痼痹痸瘐痻痶痭痵痽皙皵盝睕睟睠睒睖睚睩睧睔睙睭矠碇碚碔碏碄碕碅碆碡碃硹碙碀碖硻祼禂祽祹稑稘稙稒稗稕稢稓\"],[\"df40\",\"稛稐窣窢窞竫筦筤筭筴筩筲筥筳筱筰筡筸筶筣粲粴粯綈綆綀綍絿綅絺綎絻綃絼綌綔綄絽綒罭罫罧罨罬羦羥羧翛翜耡腤腠腷腜腩腛腢腲朡腞腶腧腯\"],[\"dfa1\",\"腄腡舝艉艄艀艂艅蓱萿葖葶葹蒏蒍葥葑葀蒆葧萰葍葽葚葙葴葳葝蔇葞萷萺萴葺葃葸萲葅萩菙葋萯葂萭葟葰萹葎葌葒葯蓅蒎萻葇萶萳葨葾葄萫葠葔葮葐蜋蜄蛷蜌蛺蛖蛵蝍蛸蜎蜉蜁蛶蜍蜅裖裋裍裎裞裛裚裌裐覅覛觟觥觤\"],[\"e040\",\"觡觠觢觜触詶誆詿詡訿詷誂誄詵誃誁詴詺谼豋豊豥豤豦貆貄貅賌赨赩趑趌趎趏趍趓趔趐趒跰跠跬跱跮跐跩跣跢跧跲跫跴輆軿輁輀輅輇輈輂輋遒逿\"],[\"e0a1\",\"遄遉逽鄐鄍鄏鄑鄖鄔鄋鄎酮酯鉈鉒鈰鈺鉦鈳鉥鉞銃鈮鉊鉆鉭鉬鉏鉠鉧鉯鈶鉡鉰鈱鉔鉣鉐鉲鉎鉓鉌鉖鈲閟閜閞閛隒隓隑隗雎雺雽雸雵靳靷靸靲頏頍頎颬飶飹馯馲馰馵骭骫魛鳪鳭鳧麀黽僦僔僗僨僳僛僪僝僤僓僬僰僯僣僠\"],[\"e140\",\"凘劀劁勩勫匰厬嘧嘕嘌嘒嗼嘏嘜嘁嘓嘂嗺嘝嘄嗿嗹墉塼墐墘墆墁塿塴墋塺墇墑墎塶墂墈塻墔墏壾奫嫜嫮嫥嫕嫪嫚嫭嫫嫳嫢嫠嫛嫬嫞嫝嫙嫨嫟孷寠\"],[\"e1a1\",\"寣屣嶂嶀嵽嶆嵺嶁嵷嶊嶉嶈嵾嵼嶍嵹嵿幘幙幓廘廑廗廎廜廕廙廒廔彄彃彯徶愬愨慁慞慱慳慒慓慲慬憀慴慔慺慛慥愻慪慡慖戩戧戫搫摍摛摝摴摶摲摳摽摵摦撦摎撂摞摜摋摓摠摐摿搿摬摫摙摥摷敳斠暡暠暟朅朄朢榱榶槉\"],[\"e240\",\"榠槎榖榰榬榼榑榙榎榧榍榩榾榯榿槄榽榤槔榹槊榚槏榳榓榪榡榞槙榗榐槂榵榥槆歊歍歋殞殟殠毃毄毾滎滵滱漃漥滸漷滻漮漉潎漙漚漧漘漻漒滭漊\"],[\"e2a1\",\"漶潳滹滮漭潀漰漼漵滫漇漎潃漅滽滶漹漜滼漺漟漍漞漈漡熇熐熉熀熅熂熏煻熆熁熗牄牓犗犕犓獃獍獑獌瑢瑳瑱瑵瑲瑧瑮甀甂甃畽疐瘖瘈瘌瘕瘑瘊瘔皸瞁睼瞅瞂睮瞀睯睾瞃碲碪碴碭碨硾碫碞碥碠碬碢碤禘禊禋禖禕禔禓\"],[\"e340\",\"禗禈禒禐稫穊稰稯稨稦窨窫窬竮箈箜箊箑箐箖箍箌箛箎箅箘劄箙箤箂粻粿粼粺綧綷緂綣綪緁緀緅綝緎緄緆緋緌綯綹綖綼綟綦綮綩綡緉罳翢翣翥翞\"],[\"e3a1\",\"耤聝聜膉膆膃膇膍膌膋舕蒗蒤蒡蒟蒺蓎蓂蒬蒮蒫蒹蒴蓁蓍蒪蒚蒱蓐蒝蒧蒻蒢蒔蓇蓌蒛蒩蒯蒨蓖蒘蒶蓏蒠蓗蓔蓒蓛蒰蒑虡蜳蜣蜨蝫蝀蜮蜞蜡蜙蜛蝃蜬蝁蜾蝆蜠蜲蜪蜭蜼蜒蜺蜱蜵蝂蜦蜧蜸蜤蜚蜰蜑裷裧裱裲裺裾裮裼裶裻\"],[\"e440\",\"裰裬裫覝覡覟覞觩觫觨誫誙誋誒誏誖谽豨豩賕賏賗趖踉踂跿踍跽踊踃踇踆踅跾踀踄輐輑輎輍鄣鄜鄠鄢鄟鄝鄚鄤鄡鄛酺酲酹酳銥銤鉶銛鉺銠銔銪銍\"],[\"e4a1\",\"銦銚銫鉹銗鉿銣鋮銎銂銕銢鉽銈銡銊銆銌銙銧鉾銇銩銝銋鈭隞隡雿靘靽靺靾鞃鞀鞂靻鞄鞁靿韎韍頖颭颮餂餀餇馝馜駃馹馻馺駂馽駇骱髣髧鬾鬿魠魡魟鳱鳲鳵麧僿儃儰僸儆儇僶僾儋儌僽儊劋劌勱勯噈噂噌嘵噁噊噉噆噘\"],[\"e540\",\"噚噀嘳嘽嘬嘾嘸嘪嘺圚墫墝墱墠墣墯墬墥墡壿嫿嫴嫽嫷嫶嬃嫸嬂嫹嬁嬇嬅嬏屧嶙嶗嶟嶒嶢嶓嶕嶠嶜嶡嶚嶞幩幝幠幜緳廛廞廡彉徲憋憃慹憱憰憢憉\"],[\"e5a1\",\"憛憓憯憭憟憒憪憡憍慦憳戭摮摰撖撠撅撗撜撏撋撊撌撣撟摨撱撘敶敺敹敻斲斳暵暰暩暲暷暪暯樀樆樗槥槸樕槱槤樠槿槬槢樛樝槾樧槲槮樔槷槧橀樈槦槻樍槼槫樉樄樘樥樏槶樦樇槴樖歑殥殣殢殦氁氀毿氂潁漦潾澇濆澒\"],[\"e640\",\"澍澉澌潢潏澅潚澖潶潬澂潕潲潒潐潗澔澓潝漀潡潫潽潧澐潓澋潩潿澕潣潷潪潻熲熯熛熰熠熚熩熵熝熥熞熤熡熪熜熧熳犘犚獘獒獞獟獠獝獛獡獚獙\"],[\"e6a1\",\"獢璇璉璊璆璁瑽璅璈瑼瑹甈甇畾瘥瘞瘙瘝瘜瘣瘚瘨瘛皜皝皞皛瞍瞏瞉瞈磍碻磏磌磑磎磔磈磃磄磉禚禡禠禜禢禛歶稹窲窴窳箷篋箾箬篎箯箹篊箵糅糈糌糋緷緛緪緧緗緡縃緺緦緶緱緰緮緟罶羬羰羭翭翫翪翬翦翨聤聧膣膟\"],[\"e740\",\"膞膕膢膙膗舖艏艓艒艐艎艑蔤蔻蔏蔀蔩蔎蔉蔍蔟蔊蔧蔜蓻蔫蓺蔈蔌蓴蔪蓲蔕蓷蓫蓳蓼蔒蓪蓩蔖蓾蔨蔝蔮蔂蓽蔞蓶蔱蔦蓧蓨蓰蓯蓹蔘蔠蔰蔋蔙蔯虢\"],[\"e7a1\",\"蝖蝣蝤蝷蟡蝳蝘蝔蝛蝒蝡蝚蝑蝞蝭蝪蝐蝎蝟蝝蝯蝬蝺蝮蝜蝥蝏蝻蝵蝢蝧蝩衚褅褌褔褋褗褘褙褆褖褑褎褉覢覤覣觭觰觬諏諆誸諓諑諔諕誻諗誾諀諅諘諃誺誽諙谾豍貏賥賟賙賨賚賝賧趠趜趡趛踠踣踥踤踮踕踛踖踑踙踦踧\"],[\"e840\",\"踔踒踘踓踜踗踚輬輤輘輚輠輣輖輗遳遰遯遧遫鄯鄫鄩鄪鄲鄦鄮醅醆醊醁醂醄醀鋐鋃鋄鋀鋙銶鋏鋱鋟鋘鋩鋗鋝鋌鋯鋂鋨鋊鋈鋎鋦鋍鋕鋉鋠鋞鋧鋑鋓\"],[\"e8a1\",\"銵鋡鋆銴镼閬閫閮閰隤隢雓霅霈霂靚鞊鞎鞈韐韏頞頝頦頩頨頠頛頧颲餈飺餑餔餖餗餕駜駍駏駓駔駎駉駖駘駋駗駌骳髬髫髳髲髱魆魃魧魴魱魦魶魵魰魨魤魬鳼鳺鳽鳿鳷鴇鴀鳹鳻鴈鴅鴄麃黓鼏鼐儜儓儗儚儑凞匴叡噰噠噮\"],[\"e940\",\"噳噦噣噭噲噞噷圜圛壈墽壉墿墺壂墼壆嬗嬙嬛嬡嬔嬓嬐嬖嬨嬚嬠嬞寯嶬嶱嶩嶧嶵嶰嶮嶪嶨嶲嶭嶯嶴幧幨幦幯廩廧廦廨廥彋徼憝憨憖懅憴懆懁懌憺\"],[\"e9a1\",\"憿憸憌擗擖擐擏擉撽撉擃擛擳擙攳敿敼斢曈暾曀曊曋曏暽暻暺曌朣樴橦橉橧樲橨樾橝橭橶橛橑樨橚樻樿橁橪橤橐橏橔橯橩橠樼橞橖橕橍橎橆歕歔歖殧殪殫毈毇氄氃氆澭濋澣濇澼濎濈潞濄澽澞濊澨瀄澥澮澺澬澪濏澿澸\"],[\"ea40\",\"澢濉澫濍澯澲澰燅燂熿熸燖燀燁燋燔燊燇燏熽燘熼燆燚燛犝犞獩獦獧獬獥獫獪瑿璚璠璔璒璕璡甋疀瘯瘭瘱瘽瘳瘼瘵瘲瘰皻盦瞚瞝瞡瞜瞛瞢瞣瞕瞙\"],[\"eaa1\",\"瞗磝磩磥磪磞磣磛磡磢磭磟磠禤穄穈穇窶窸窵窱窷篞篣篧篝篕篥篚篨篹篔篪篢篜篫篘篟糒糔糗糐糑縒縡縗縌縟縠縓縎縜縕縚縢縋縏縖縍縔縥縤罃罻罼罺羱翯耪耩聬膱膦膮膹膵膫膰膬膴膲膷膧臲艕艖艗蕖蕅蕫蕍蕓蕡蕘\"],[\"eb40\",\"蕀蕆蕤蕁蕢蕄蕑蕇蕣蔾蕛蕱蕎蕮蕵蕕蕧蕠薌蕦蕝蕔蕥蕬虣虥虤螛螏螗螓螒螈螁螖螘蝹螇螣螅螐螑螝螄螔螜螚螉褞褦褰褭褮褧褱褢褩褣褯褬褟觱諠\"],[\"eba1\",\"諢諲諴諵諝謔諤諟諰諈諞諡諨諿諯諻貑貒貐賵賮賱賰賳赬赮趥趧踳踾踸蹀蹅踶踼踽蹁踰踿躽輶輮輵輲輹輷輴遶遹遻邆郺鄳鄵鄶醓醐醑醍醏錧錞錈錟錆錏鍺錸錼錛錣錒錁鍆錭錎錍鋋錝鋺錥錓鋹鋷錴錂錤鋿錩錹錵錪錔錌\"],[\"ec40\",\"錋鋾錉錀鋻錖閼闍閾閹閺閶閿閵閽隩雔霋霒霐鞙鞗鞔韰韸頵頯頲餤餟餧餩馞駮駬駥駤駰駣駪駩駧骹骿骴骻髶髺髹髷鬳鮀鮅鮇魼魾魻鮂鮓鮒鮐魺鮕\"],[\"eca1\",\"魽鮈鴥鴗鴠鴞鴔鴩鴝鴘鴢鴐鴙鴟麈麆麇麮麭黕黖黺鼒鼽儦儥儢儤儠儩勴嚓嚌嚍嚆嚄嚃噾嚂噿嚁壖壔壏壒嬭嬥嬲嬣嬬嬧嬦嬯嬮孻寱寲嶷幬幪徾徻懃憵憼懧懠懥懤懨懞擯擩擣擫擤擨斁斀斶旚曒檍檖檁檥檉檟檛檡檞檇檓檎\"],[\"ed40\",\"檕檃檨檤檑橿檦檚檅檌檒歛殭氉濌澩濴濔濣濜濭濧濦濞濲濝濢濨燡燱燨燲燤燰燢獳獮獯璗璲璫璐璪璭璱璥璯甐甑甒甏疄癃癈癉癇皤盩瞵瞫瞲瞷瞶\"],[\"eda1\",\"瞴瞱瞨矰磳磽礂磻磼磲礅磹磾礄禫禨穜穛穖穘穔穚窾竀竁簅簏篲簀篿篻簎篴簋篳簂簉簃簁篸篽簆篰篱簐簊糨縭縼繂縳顈縸縪繉繀繇縩繌縰縻縶繄縺罅罿罾罽翴翲耬膻臄臌臊臅臇膼臩艛艚艜薃薀薏薧薕薠薋薣蕻薤薚薞\"],[\"ee40\",\"蕷蕼薉薡蕺蕸蕗薎薖薆薍薙薝薁薢薂薈薅蕹蕶薘薐薟虨螾螪螭蟅螰螬螹螵螼螮蟉蟃蟂蟌螷螯蟄蟊螴螶螿螸螽蟞螲褵褳褼褾襁襒褷襂覭覯覮觲觳謞\"],[\"eea1\",\"謘謖謑謅謋謢謏謒謕謇謍謈謆謜謓謚豏豰豲豱豯貕貔賹赯蹎蹍蹓蹐蹌蹇轃轀邅遾鄸醚醢醛醙醟醡醝醠鎡鎃鎯鍤鍖鍇鍼鍘鍜鍶鍉鍐鍑鍠鍭鎏鍌鍪鍹鍗鍕鍒鍏鍱鍷鍻鍡鍞鍣鍧鎀鍎鍙闇闀闉闃闅閷隮隰隬霠霟霘霝霙鞚鞡鞜\"],[\"ef40\",\"鞞鞝韕韔韱顁顄顊顉顅顃餥餫餬餪餳餲餯餭餱餰馘馣馡騂駺駴駷駹駸駶駻駽駾駼騃骾髾髽鬁髼魈鮚鮨鮞鮛鮦鮡鮥鮤鮆鮢鮠鮯鴳鵁鵧鴶鴮鴯鴱鴸鴰\"],[\"efa1\",\"鵅鵂鵃鴾鴷鵀鴽翵鴭麊麉麍麰黈黚黻黿鼤鼣鼢齔龠儱儭儮嚘嚜嚗嚚嚝嚙奰嬼屩屪巀幭幮懘懟懭懮懱懪懰懫懖懩擿攄擽擸攁攃擼斔旛曚曛曘櫅檹檽櫡櫆檺檶檷櫇檴檭歞毉氋瀇瀌瀍瀁瀅瀔瀎濿瀀濻瀦濼濷瀊爁燿燹爃燽獶\"],[\"f040\",\"璸瓀璵瓁璾璶璻瓂甔甓癜癤癙癐癓癗癚皦皽盬矂瞺磿礌礓礔礉礐礒礑禭禬穟簜簩簙簠簟簭簝簦簨簢簥簰繜繐繖繣繘繢繟繑繠繗繓羵羳翷翸聵臑臒\"],[\"f0a1\",\"臐艟艞薴藆藀藃藂薳薵薽藇藄薿藋藎藈藅薱薶藒蘤薸薷薾虩蟧蟦蟢蟛蟫蟪蟥蟟蟳蟤蟔蟜蟓蟭蟘蟣螤蟗蟙蠁蟴蟨蟝襓襋襏襌襆襐襑襉謪謧謣謳謰謵譇謯謼謾謱謥謷謦謶謮謤謻謽謺豂豵貙貘貗賾贄贂贀蹜蹢蹠蹗蹖蹞蹥蹧\"],[\"f140\",\"蹛蹚蹡蹝蹩蹔轆轇轈轋鄨鄺鄻鄾醨醥醧醯醪鎵鎌鎒鎷鎛鎝鎉鎧鎎鎪鎞鎦鎕鎈鎙鎟鎍鎱鎑鎲鎤鎨鎴鎣鎥闒闓闑隳雗雚巂雟雘雝霣霢霥鞬鞮鞨鞫鞤鞪\"],[\"f1a1\",\"鞢鞥韗韙韖韘韺顐顑顒颸饁餼餺騏騋騉騍騄騑騊騅騇騆髀髜鬈鬄鬅鬩鬵魊魌魋鯇鯆鯃鮿鯁鮵鮸鯓鮶鯄鮹鮽鵜鵓鵏鵊鵛鵋鵙鵖鵌鵗鵒鵔鵟鵘鵚麎麌黟鼁鼀鼖鼥鼫鼪鼩鼨齌齕儴儵劖勷厴嚫嚭嚦嚧嚪嚬壚壝壛夒嬽嬾嬿巃幰\"],[\"f240\",\"徿懻攇攐攍攉攌攎斄旞旝曞櫧櫠櫌櫑櫙櫋櫟櫜櫐櫫櫏櫍櫞歠殰氌瀙瀧瀠瀖瀫瀡瀢瀣瀩瀗瀤瀜瀪爌爊爇爂爅犥犦犤犣犡瓋瓅璷瓃甖癠矉矊矄矱礝礛\"],[\"f2a1\",\"礡礜礗礞禰穧穨簳簼簹簬簻糬糪繶繵繸繰繷繯繺繲繴繨罋罊羃羆羷翽翾聸臗臕艤艡艣藫藱藭藙藡藨藚藗藬藲藸藘藟藣藜藑藰藦藯藞藢蠀蟺蠃蟶蟷蠉蠌蠋蠆蟼蠈蟿蠊蠂襢襚襛襗襡襜襘襝襙覈覷覶觶譐譈譊譀譓譖譔譋譕\"],[\"f340\",\"譑譂譒譗豃豷豶貚贆贇贉趬趪趭趫蹭蹸蹳蹪蹯蹻軂轒轑轏轐轓辴酀鄿醰醭鏞鏇鏏鏂鏚鏐鏹鏬鏌鏙鎩鏦鏊鏔鏮鏣鏕鏄鏎鏀鏒鏧镽闚闛雡霩霫霬霨霦\"],[\"f3a1\",\"鞳鞷鞶韝韞韟顜顙顝顗颿颽颻颾饈饇饃馦馧騚騕騥騝騤騛騢騠騧騣騞騜騔髂鬋鬊鬎鬌鬷鯪鯫鯠鯞鯤鯦鯢鯰鯔鯗鯬鯜鯙鯥鯕鯡鯚鵷鶁鶊鶄鶈鵱鶀鵸鶆鶋鶌鵽鵫鵴鵵鵰鵩鶅鵳鵻鶂鵯鵹鵿鶇鵨麔麑黀黼鼭齀齁齍齖齗齘匷嚲\"],[\"f440\",\"嚵嚳壣孅巆巇廮廯忀忁懹攗攖攕攓旟曨曣曤櫳櫰櫪櫨櫹櫱櫮櫯瀼瀵瀯瀷瀴瀱灂瀸瀿瀺瀹灀瀻瀳灁爓爔犨獽獼璺皫皪皾盭矌矎矏矍矲礥礣礧礨礤礩\"],[\"f4a1\",\"禲穮穬穭竷籉籈籊籇籅糮繻繾纁纀羺翿聹臛臙舋艨艩蘢藿蘁藾蘛蘀藶蘄蘉蘅蘌藽蠙蠐蠑蠗蠓蠖襣襦覹觷譠譪譝譨譣譥譧譭趮躆躈躄轙轖轗轕轘轚邍酃酁醷醵醲醳鐋鐓鏻鐠鐏鐔鏾鐕鐐鐨鐙鐍鏵鐀鏷鐇鐎鐖鐒鏺鐉鏸鐊鏿\"],[\"f540\",\"鏼鐌鏶鐑鐆闞闠闟霮霯鞹鞻韽韾顠顢顣顟飁飂饐饎饙饌饋饓騲騴騱騬騪騶騩騮騸騭髇髊髆鬐鬒鬑鰋鰈鯷鰅鰒鯸鱀鰇鰎鰆鰗鰔鰉鶟鶙鶤鶝鶒鶘鶐鶛\"],[\"f5a1\",\"鶠鶔鶜鶪鶗鶡鶚鶢鶨鶞鶣鶿鶩鶖鶦鶧麙麛麚黥黤黧黦鼰鼮齛齠齞齝齙龑儺儹劘劗囃嚽嚾孈孇巋巏廱懽攛欂櫼欃櫸欀灃灄灊灈灉灅灆爝爚爙獾甗癪矐礭礱礯籔籓糲纊纇纈纋纆纍罍羻耰臝蘘蘪蘦蘟蘣蘜蘙蘧蘮蘡蘠蘩蘞蘥\"],[\"f640\",\"蠩蠝蠛蠠蠤蠜蠫衊襭襩襮襫觺譹譸譅譺譻贐贔趯躎躌轞轛轝酆酄酅醹鐿鐻鐶鐩鐽鐼鐰鐹鐪鐷鐬鑀鐱闥闤闣霵霺鞿韡顤飉飆飀饘饖騹騽驆驄驂驁騺\"],[\"f6a1\",\"騿髍鬕鬗鬘鬖鬺魒鰫鰝鰜鰬鰣鰨鰩鰤鰡鶷鶶鶼鷁鷇鷊鷏鶾鷅鷃鶻鶵鷎鶹鶺鶬鷈鶱鶭鷌鶳鷍鶲鹺麜黫黮黭鼛鼘鼚鼱齎齥齤龒亹囆囅囋奱孋孌巕巑廲攡攠攦攢欋欈欉氍灕灖灗灒爞爟犩獿瓘瓕瓙瓗癭皭礵禴穰穱籗籜籙籛籚\"],[\"f740\",\"糴糱纑罏羇臞艫蘴蘵蘳蘬蘲蘶蠬蠨蠦蠪蠥襱覿覾觻譾讄讂讆讅譿贕躕躔躚躒躐躖躗轠轢酇鑌鑐鑊鑋鑏鑇鑅鑈鑉鑆霿韣顪顩飋饔饛驎驓驔驌驏驈驊\"],[\"f7a1\",\"驉驒驐髐鬙鬫鬻魖魕鱆鱈鰿鱄鰹鰳鱁鰼鰷鰴鰲鰽鰶鷛鷒鷞鷚鷋鷐鷜鷑鷟鷩鷙鷘鷖鷵鷕鷝麶黰鼵鼳鼲齂齫龕龢儽劙壨壧奲孍巘蠯彏戁戃戄攩攥斖曫欑欒欏毊灛灚爢玂玁玃癰矔籧籦纕艬蘺虀蘹蘼蘱蘻蘾蠰蠲蠮蠳襶襴襳觾\"],[\"f840\",\"讌讎讋讈豅贙躘轤轣醼鑢鑕鑝鑗鑞韄韅頀驖驙鬞鬟鬠鱒鱘鱐鱊鱍鱋鱕鱙鱌鱎鷻鷷鷯鷣鷫鷸鷤鷶鷡鷮鷦鷲鷰鷢鷬鷴鷳鷨鷭黂黐黲黳鼆鼜鼸鼷鼶齃齏\"],[\"f8a1\",\"齱齰齮齯囓囍孎屭攭曭曮欓灟灡灝灠爣瓛瓥矕礸禷禶籪纗羉艭虃蠸蠷蠵衋讔讕躞躟躠躝醾醽釂鑫鑨鑩雥靆靃靇韇韥驞髕魙鱣鱧鱦鱢鱞鱠鸂鷾鸇鸃鸆鸅鸀鸁鸉鷿鷽鸄麠鼞齆齴齵齶囔攮斸欘欙欗欚灢爦犪矘矙礹籩籫糶纚\"],[\"f940\",\"纘纛纙臠臡虆虇虈襹襺襼襻觿讘讙躥躤躣鑮鑭鑯鑱鑳靉顲饟鱨鱮鱭鸋鸍鸐鸏鸒鸑麡黵鼉齇齸齻齺齹圞灦籯蠼趲躦釃鑴鑸鑶鑵驠鱴鱳鱱鱵鸔鸓黶鼊\"],[\"f9a1\",\"龤灨灥糷虪蠾蠽蠿讞貜躩軉靋顳顴飌饡馫驤驦驧鬤鸕鸗齈戇欞爧虌躨钂钀钁驩驨鬮鸙爩虋讟钃鱹麷癵驫鱺鸝灩灪麤齾齉龘碁銹裏墻恒粧嫺╔╦╗╠╬╣╚╩╝╒╤╕╞╪╡╘╧╛╓╥╖╟╫╢╙╨╜║═╭╮╰╯▓\"]]");

/***/ }),
/* 252 */
/***/ (function(module, exports, __webpack_require__) {

// Generated by CoffeeScript 1.7.1
(function() {
  var ArrayT, NumberT, utils;

  NumberT = __webpack_require__(89).Number;

  utils = __webpack_require__(40);

  ArrayT = (function() {
    function ArrayT(type, length, lengthType) {
      this.type = type;
      this.length = length;
      this.lengthType = lengthType != null ? lengthType : 'count';
    }

    ArrayT.prototype.decode = function(stream, parent) {
      var ctx, i, length, pos, res, target, _i;
      pos = stream.pos;
      res = [];
      ctx = parent;
      if (this.length != null) {
        length = utils.resolveLength(this.length, stream, parent);
      }
      if (this.length instanceof NumberT) {
        Object.defineProperties(res, {
          parent: {
            value: parent
          },
          _startOffset: {
            value: pos
          },
          _currentOffset: {
            value: 0,
            writable: true
          },
          _length: {
            value: length
          }
        });
        ctx = res;
      }
      if ((length == null) || this.lengthType === 'bytes') {
        target = length != null ? stream.pos + length : (parent != null ? parent._length : void 0) ? parent._startOffset + parent._length : stream.length;
        while (stream.pos < target) {
          res.push(this.type.decode(stream, ctx));
        }
      } else {
        for (i = _i = 0; _i < length; i = _i += 1) {
          res.push(this.type.decode(stream, ctx));
        }
      }
      return res;
    };

    ArrayT.prototype.size = function(array, ctx) {
      var item, size, _i, _len;
      if (!array) {
        return this.type.size(null, ctx) * utils.resolveLength(this.length, null, ctx);
      }
      size = 0;
      if (this.length instanceof NumberT) {
        size += this.length.size();
        ctx = {
          parent: ctx
        };
      }
      for (_i = 0, _len = array.length; _i < _len; _i++) {
        item = array[_i];
        size += this.type.size(item, ctx);
      }
      return size;
    };

    ArrayT.prototype.encode = function(stream, array, parent) {
      var ctx, i, item, ptr, _i, _len;
      ctx = parent;
      if (this.length instanceof NumberT) {
        ctx = {
          pointers: [],
          startOffset: stream.pos,
          parent: parent
        };
        ctx.pointerOffset = stream.pos + this.size(array, ctx);
        this.length.encode(stream, array.length);
      }
      for (_i = 0, _len = array.length; _i < _len; _i++) {
        item = array[_i];
        this.type.encode(stream, item, ctx);
      }
      if (this.length instanceof NumberT) {
        i = 0;
        while (i < ctx.pointers.length) {
          ptr = ctx.pointers[i++];
          ptr.type.encode(stream, ptr.val);
        }
      }
    };

    return ArrayT;

  })();

  module.exports = ArrayT;

}).call(this);


/***/ }),
/* 253 */
/***/ (function(module, exports, __webpack_require__) {

// Generated by CoffeeScript 1.7.1
(function() {
  var Struct, utils;

  utils = __webpack_require__(40);

  Struct = (function() {
    function Struct(fields) {
      this.fields = fields != null ? fields : {};
    }

    Struct.prototype.decode = function(stream, parent, length) {
      var res, _ref;
      if (length == null) {
        length = 0;
      }
      res = this._setup(stream, parent, length);
      this._parseFields(stream, res, this.fields);
      if ((_ref = this.process) != null) {
        _ref.call(res, stream);
      }
      return res;
    };

    Struct.prototype._setup = function(stream, parent, length) {
      var res;
      res = {};
      Object.defineProperties(res, {
        parent: {
          value: parent
        },
        _startOffset: {
          value: stream.pos
        },
        _currentOffset: {
          value: 0,
          writable: true
        },
        _length: {
          value: length
        }
      });
      return res;
    };

    Struct.prototype._parseFields = function(stream, res, fields) {
      var key, type, val;
      for (key in fields) {
        type = fields[key];
        if (typeof type === 'function') {
          val = type.call(res, res);
        } else {
          val = type.decode(stream, res);
        }
        if (val !== void 0) {
          if (val instanceof utils.PropertyDescriptor) {
            Object.defineProperty(res, key, val);
          } else {
            res[key] = val;
          }
        }
        res._currentOffset = stream.pos - res._startOffset;
      }
    };

    Struct.prototype.size = function(val, parent, includePointers) {
      var ctx, key, size, type, _ref;
      if (val == null) {
        val = {};
      }
      if (includePointers == null) {
        includePointers = true;
      }
      ctx = {
        parent: parent,
        val: val,
        pointerSize: 0
      };
      size = 0;
      _ref = this.fields;
      for (key in _ref) {
        type = _ref[key];
        if (type.size != null) {
          size += type.size(val[key], ctx);
        }
      }
      if (includePointers) {
        size += ctx.pointerSize;
      }
      return size;
    };

    Struct.prototype.encode = function(stream, val, parent) {
      var ctx, i, key, ptr, type, _ref, _ref1;
      if ((_ref = this.preEncode) != null) {
        _ref.call(val, stream);
      }
      ctx = {
        pointers: [],
        startOffset: stream.pos,
        parent: parent,
        val: val,
        pointerSize: 0
      };
      ctx.pointerOffset = stream.pos + this.size(val, ctx, false);
      _ref1 = this.fields;
      for (key in _ref1) {
        type = _ref1[key];
        if (type.encode != null) {
          type.encode(stream, val[key], ctx);
        }
      }
      i = 0;
      while (i < ctx.pointers.length) {
        ptr = ctx.pointers[i++];
        ptr.type.encode(stream, ptr.val, ptr.parent);
      }
    };

    return Struct;

  })();

  module.exports = Struct;

}).call(this);


/***/ }),
/* 254 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = !__webpack_require__(23) && !__webpack_require__(52)(function () {
  return Object.defineProperty(__webpack_require__(176)('div'), 'a', { get: function () { return 7; } }).a != 7;
});


/***/ }),
/* 255 */
/***/ (function(module, exports) {

module.exports = function (done, value) {
  return { value: value, done: !!done };
};


/***/ }),
/* 256 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = __webpack_require__(41);


/***/ }),
/* 257 */
/***/ (function(module, exports, __webpack_require__) {

var dP = __webpack_require__(26);
var anObject = __webpack_require__(29);
var getKeys = __webpack_require__(105);

module.exports = __webpack_require__(23) ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject(O);
  var keys = getKeys(Properties);
  var length = keys.length;
  var i = 0;
  var P;
  while (length > i) dP.f(O, P = keys[i++], Properties[P]);
  return O;
};


/***/ }),
/* 258 */
/***/ (function(module, exports, __webpack_require__) {

var has = __webpack_require__(51);
var toIObject = __webpack_require__(50);
var arrayIndexOf = __webpack_require__(425)(false);
var IE_PROTO = __webpack_require__(180)('IE_PROTO');

module.exports = function (object, names) {
  var O = toIObject(object);
  var i = 0;
  var result = [];
  var key;
  for (key in O) if (key != IE_PROTO) has(O, key) && result.push(key);
  // Don't enum bug & hidden keys
  while (names.length > i) if (has(O, key = names[i++])) {
    ~arrayIndexOf(result, key) || result.push(key);
  }
  return result;
};


/***/ }),
/* 259 */
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__(179);
var max = Math.max;
var min = Math.min;
module.exports = function (index, length) {
  index = toInteger(index);
  return index < 0 ? max(index + length, 0) : min(index, length);
};


/***/ }),
/* 260 */
/***/ (function(module, exports, __webpack_require__) {

var document = __webpack_require__(17).document;
module.exports = document && document.documentElement;


/***/ }),
/* 261 */
/***/ (function(module, exports, __webpack_require__) {

// 7.2.2 IsArray(argument)
var cof = __webpack_require__(99);
module.exports = Array.isArray || function isArray(arg) {
  return cof(arg) == 'Array';
};


/***/ }),
/* 262 */
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.7 / 15.2.3.4 Object.getOwnPropertyNames(O)
var $keys = __webpack_require__(258);
var hiddenKeys = __webpack_require__(182).concat('length', 'prototype');

exports.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return $keys(O, hiddenKeys);
};


/***/ }),
/* 263 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(444), __esModule: true };

/***/ }),
/* 264 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var dP = __webpack_require__(26).f;
var create = __webpack_require__(129);
var redefineAll = __webpack_require__(189);
var ctx = __webpack_require__(33);
var anInstance = __webpack_require__(190);
var forOf = __webpack_require__(107);
var $iterDefine = __webpack_require__(178);
var step = __webpack_require__(255);
var setSpecies = __webpack_require__(267);
var DESCRIPTORS = __webpack_require__(23);
var fastKey = __webpack_require__(132).fastKey;
var validate = __webpack_require__(191);
var SIZE = DESCRIPTORS ? '_s' : 'size';

var getEntry = function (that, key) {
  // fast case
  var index = fastKey(key);
  var entry;
  if (index !== 'F') return that._i[index];
  // frozen object case
  for (entry = that._f; entry; entry = entry.n) {
    if (entry.k == key) return entry;
  }
};

module.exports = {
  getConstructor: function (wrapper, NAME, IS_MAP, ADDER) {
    var C = wrapper(function (that, iterable) {
      anInstance(that, C, NAME, '_i');
      that._t = NAME;         // collection type
      that._i = create(null); // index
      that._f = undefined;    // first entry
      that._l = undefined;    // last entry
      that[SIZE] = 0;         // size
      if (iterable != undefined) forOf(iterable, IS_MAP, that[ADDER], that);
    });
    redefineAll(C.prototype, {
      // 23.1.3.1 Map.prototype.clear()
      // 23.2.3.2 Set.prototype.clear()
      clear: function clear() {
        for (var that = validate(this, NAME), data = that._i, entry = that._f; entry; entry = entry.n) {
          entry.r = true;
          if (entry.p) entry.p = entry.p.n = undefined;
          delete data[entry.i];
        }
        that._f = that._l = undefined;
        that[SIZE] = 0;
      },
      // 23.1.3.3 Map.prototype.delete(key)
      // 23.2.3.4 Set.prototype.delete(value)
      'delete': function (key) {
        var that = validate(this, NAME);
        var entry = getEntry(that, key);
        if (entry) {
          var next = entry.n;
          var prev = entry.p;
          delete that._i[entry.i];
          entry.r = true;
          if (prev) prev.n = next;
          if (next) next.p = prev;
          if (that._f == entry) that._f = next;
          if (that._l == entry) that._l = prev;
          that[SIZE]--;
        } return !!entry;
      },
      // 23.2.3.6 Set.prototype.forEach(callbackfn, thisArg = undefined)
      // 23.1.3.5 Map.prototype.forEach(callbackfn, thisArg = undefined)
      forEach: function forEach(callbackfn /* , that = undefined */) {
        validate(this, NAME);
        var f = ctx(callbackfn, arguments.length > 1 ? arguments[1] : undefined, 3);
        var entry;
        while (entry = entry ? entry.n : this._f) {
          f(entry.v, entry.k, this);
          // revert to the last existing entry
          while (entry && entry.r) entry = entry.p;
        }
      },
      // 23.1.3.7 Map.prototype.has(key)
      // 23.2.3.7 Set.prototype.has(value)
      has: function has(key) {
        return !!getEntry(validate(this, NAME), key);
      }
    });
    if (DESCRIPTORS) dP(C.prototype, 'size', {
      get: function () {
        return validate(this, NAME)[SIZE];
      }
    });
    return C;
  },
  def: function (that, key, value) {
    var entry = getEntry(that, key);
    var prev, index;
    // change existing entry
    if (entry) {
      entry.v = value;
    // create new entry
    } else {
      that._l = entry = {
        i: index = fastKey(key, true), // <- index
        k: key,                        // <- key
        v: value,                      // <- value
        p: prev = that._l,             // <- previous entry
        n: undefined,                  // <- next entry
        r: false                       // <- removed
      };
      if (!that._f) that._f = entry;
      if (prev) prev.n = entry;
      that[SIZE]++;
      // add to index
      if (index !== 'F') that._i[index] = entry;
    } return that;
  },
  getEntry: getEntry,
  setStrong: function (C, NAME, IS_MAP) {
    // add .keys, .values, .entries, [@@iterator]
    // 23.1.3.4, 23.1.3.8, 23.1.3.11, 23.1.3.12, 23.2.3.5, 23.2.3.8, 23.2.3.10, 23.2.3.11
    $iterDefine(C, NAME, function (iterated, kind) {
      this._t = validate(iterated, NAME); // target
      this._k = kind;                     // kind
      this._l = undefined;                // previous
    }, function () {
      var that = this;
      var kind = that._k;
      var entry = that._l;
      // revert to the last existing entry
      while (entry && entry.r) entry = entry.p;
      // get next entry
      if (!that._t || !(that._l = entry = entry ? entry.n : that._t._f)) {
        // or finish the iteration
        that._t = undefined;
        return step(1);
      }
      // return step by kind
      if (kind == 'keys') return step(0, entry.k);
      if (kind == 'values') return step(0, entry.v);
      return step(0, [entry.k, entry.v]);
    }, IS_MAP ? 'entries' : 'values', !IS_MAP, true);

    // add [@@species], 23.1.2.2, 23.2.2.2
    setSpecies(NAME);
  }
};


/***/ }),
/* 265 */
/***/ (function(module, exports, __webpack_require__) {

// call something on iterator step with safe closing on error
var anObject = __webpack_require__(29);
module.exports = function (iterator, fn, value, entries) {
  try {
    return entries ? fn(anObject(value)[0], value[1]) : fn(value);
  // 7.4.6 IteratorClose(iterator, completion)
  } catch (e) {
    var ret = iterator['return'];
    if (ret !== undefined) anObject(ret.call(iterator));
    throw e;
  }
};


/***/ }),
/* 266 */
/***/ (function(module, exports, __webpack_require__) {

// check on default Array iterator
var Iterators = __webpack_require__(103);
var ITERATOR = __webpack_require__(21)('iterator');
var ArrayProto = Array.prototype;

module.exports = function (it) {
  return it !== undefined && (Iterators.Array === it || ArrayProto[ITERATOR] === it);
};


/***/ }),
/* 267 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(17);
var core = __webpack_require__(7);
var dP = __webpack_require__(26);
var DESCRIPTORS = __webpack_require__(23);
var SPECIES = __webpack_require__(21)('species');

module.exports = function (KEY) {
  var C = typeof core[KEY] == 'function' ? core[KEY] : global[KEY];
  if (DESCRIPTORS && C && !C[SPECIES]) dP.f(C, SPECIES, {
    configurable: true,
    get: function () { return this; }
  });
};


/***/ }),
/* 268 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(17);
var $export = __webpack_require__(9);
var meta = __webpack_require__(132);
var fails = __webpack_require__(52);
var hide = __webpack_require__(41);
var redefineAll = __webpack_require__(189);
var forOf = __webpack_require__(107);
var anInstance = __webpack_require__(190);
var isObject = __webpack_require__(22);
var setToStringTag = __webpack_require__(106);
var dP = __webpack_require__(26).f;
var each = __webpack_require__(451)(0);
var DESCRIPTORS = __webpack_require__(23);

module.exports = function (NAME, wrapper, methods, common, IS_MAP, IS_WEAK) {
  var Base = global[NAME];
  var C = Base;
  var ADDER = IS_MAP ? 'set' : 'add';
  var proto = C && C.prototype;
  var O = {};
  if (!DESCRIPTORS || typeof C != 'function' || !(IS_WEAK || proto.forEach && !fails(function () {
    new C().entries().next();
  }))) {
    // create collection constructor
    C = common.getConstructor(wrapper, NAME, IS_MAP, ADDER);
    redefineAll(C.prototype, methods);
    meta.NEED = true;
  } else {
    C = wrapper(function (target, iterable) {
      anInstance(target, C, NAME, '_c');
      target._c = new Base();
      if (iterable != undefined) forOf(iterable, IS_MAP, target[ADDER], target);
    });
    each('add,clear,delete,forEach,get,has,set,keys,values,entries,toJSON'.split(','), function (KEY) {
      var IS_ADDER = KEY == 'add' || KEY == 'set';
      if (KEY in proto && !(IS_WEAK && KEY == 'clear')) hide(C.prototype, KEY, function (a, b) {
        anInstance(this, C, KEY);
        if (!IS_ADDER && IS_WEAK && !isObject(a)) return KEY == 'get' ? undefined : false;
        var result = this._c[KEY](a === 0 ? 0 : a, b);
        return IS_ADDER ? this : result;
      });
    });
    IS_WEAK || dP(C.prototype, 'size', {
      get: function () {
        return this._c.size;
      }
    });
  }

  setToStringTag(C, NAME);

  O[NAME] = C;
  $export($export.G + $export.W + $export.F, O);

  if (!IS_WEAK) common.setStrong(C, NAME, IS_MAP);

  return C;
};


/***/ }),
/* 269 */
/***/ (function(module, exports, __webpack_require__) {

// https://github.com/DavidBruant/Map-Set.prototype.toJSON
var classof = __webpack_require__(184);
var from = __webpack_require__(455);
module.exports = function (NAME) {
  return function toJSON() {
    if (classof(this) != NAME) throw TypeError(NAME + "#toJSON isn't generic");
    return from(this);
  };
};


/***/ }),
/* 270 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

// https://tc39.github.io/proposal-setmap-offrom/
var $export = __webpack_require__(9);

module.exports = function (COLLECTION) {
  $export($export.S, COLLECTION, { of: function of() {
    var length = arguments.length;
    var A = new Array(length);
    while (length--) A[length] = arguments[length];
    return new this(A);
  } });
};


/***/ }),
/* 271 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

// https://tc39.github.io/proposal-setmap-offrom/
var $export = __webpack_require__(9);
var aFunction = __webpack_require__(101);
var ctx = __webpack_require__(33);
var forOf = __webpack_require__(107);

module.exports = function (COLLECTION) {
  $export($export.S, COLLECTION, { from: function from(source /* , mapFn, thisArg */) {
    var mapFn = arguments[1];
    var mapping, A, n, cb;
    aFunction(this);
    mapping = mapFn !== undefined;
    if (mapping) aFunction(mapFn);
    if (source == undefined) return new this();
    A = [];
    if (mapping) {
      n = 0;
      cb = ctx(mapFn, arguments[2], 2);
      forOf(source, false, function (nextItem) {
        A.push(cb(nextItem, n++));
      });
    } else {
      forOf(source, false, A.push, A);
    }
    return new this(A);
  } });
};


/***/ }),
/* 272 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var slice = Array.prototype.slice;
var isArgs = __webpack_require__(273);

var origKeys = Object.keys;
var keysShim = origKeys ? function keys(o) { return origKeys(o); } : __webpack_require__(471);

var originalKeys = Object.keys;

keysShim.shim = function shimObjectKeys() {
	if (Object.keys) {
		var keysWorksWithArguments = (function () {
			// Safari 5.0 bug
			var args = Object.keys(arguments);
			return args && args.length === arguments.length;
		}(1, 2));
		if (!keysWorksWithArguments) {
			Object.keys = function keys(object) { // eslint-disable-line func-name-matching
				if (isArgs(object)) {
					return originalKeys(slice.call(object));
				}
				return originalKeys(object);
			};
		}
	} else {
		Object.keys = keysShim;
	}
	return Object.keys || keysShim;
};

module.exports = keysShim;


/***/ }),
/* 273 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var toStr = Object.prototype.toString;

module.exports = function isArguments(value) {
	var str = toStr.call(value);
	var isArgs = str === '[object Arguments]';
	if (!isArgs) {
		isArgs = str !== '[object Array]' &&
			value !== null &&
			typeof value === 'object' &&
			typeof value.length === 'number' &&
			value.length >= 0 &&
			toStr.call(value.callee) === '[object Function]';
	}
	return isArgs;
};


/***/ }),
/* 274 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var bind = __webpack_require__(275);

var GetIntrinsic = __webpack_require__(475);

var $apply = GetIntrinsic('%Function.prototype.apply%');
var $call = GetIntrinsic('%Function.prototype.call%');
var $reflectApply = GetIntrinsic('%Reflect.apply%', true) || bind.call($call, $apply);

module.exports = function callBind() {
	return $reflectApply(bind, $call, arguments);
};

module.exports.apply = function applyBind() {
	return $reflectApply(bind, $apply, arguments);
};


/***/ }),
/* 275 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var implementation = __webpack_require__(474);

module.exports = Function.prototype.bind || implementation;


/***/ }),
/* 276 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(global) {

var origSymbol = global.Symbol;
var hasSymbolSham = __webpack_require__(476);

module.exports = function hasNativeSymbols() {
	if (typeof origSymbol !== 'function') { return false; }
	if (typeof Symbol !== 'function') { return false; }
	if (typeof origSymbol('foo') !== 'symbol') { return false; }
	if (typeof Symbol('bar') !== 'symbol') { return false; }

	return hasSymbolSham();
};

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(24)))

/***/ }),
/* 277 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var numberIsNaN = function (value) {
	return value !== value;
};

module.exports = function is(a, b) {
	if (a === 0 && b === 0) {
		return 1 / a === 1 / b;
	}
	if (a === b) {
		return true;
	}
	if (numberIsNaN(a) && numberIsNaN(b)) {
		return true;
	}
	return false;
};



/***/ }),
/* 278 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var implementation = __webpack_require__(277);

module.exports = function getPolyfill() {
	return typeof Object.is === 'function' ? Object.is : implementation;
};


/***/ }),
/* 279 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var $Object = Object;
var $TypeError = TypeError;

module.exports = function flags() {
	if (this != null && this !== $Object(this)) {
		throw new $TypeError('RegExp.prototype.flags getter called on non-object');
	}
	var result = '';
	if (this.global) {
		result += 'g';
	}
	if (this.ignoreCase) {
		result += 'i';
	}
	if (this.multiline) {
		result += 'm';
	}
	if (this.dotAll) {
		result += 's';
	}
	if (this.unicode) {
		result += 'u';
	}
	if (this.sticky) {
		result += 'y';
	}
	return result;
};


/***/ }),
/* 280 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var implementation = __webpack_require__(279);

var supportsDescriptors = __webpack_require__(108).supportsDescriptors;
var $gOPD = Object.getOwnPropertyDescriptor;
var $TypeError = TypeError;

module.exports = function getPolyfill() {
	if (!supportsDescriptors) {
		throw new $TypeError('RegExp.prototype.flags requires a true ES5 environment that supports property descriptors');
	}
	if ((/a/mig).flags === 'gim') {
		var descriptor = $gOPD(RegExp.prototype, 'flags');
		if (descriptor && typeof descriptor.get === 'function' && typeof (/a/).dotAll === 'boolean') {
			return descriptor.get;
		}
	}
	return implementation;
};


/***/ }),
/* 281 */
/***/ (function(module, exports, __webpack_require__) {

var ITERATOR = __webpack_require__(21)('iterator');
var SAFE_CLOSING = false;

try {
  var riter = [7][ITERATOR]();
  riter['return'] = function () { SAFE_CLOSING = true; };
  // eslint-disable-next-line no-throw-literal
  Array.from(riter, function () { throw 2; });
} catch (e) { /* empty */ }

module.exports = function (exec, skipClosing) {
  if (!skipClosing && !SAFE_CLOSING) return false;
  var safe = false;
  try {
    var arr = [7];
    var iter = arr[ITERATOR]();
    iter.next = function () { return { done: safe = true }; };
    arr[ITERATOR] = function () { return iter; };
    exec(arr);
  } catch (e) { /* empty */ }
  return safe;
};


/***/ }),
/* 282 */
/***/ (function(module, exports, __webpack_require__) {

// 7.3.20 SpeciesConstructor(O, defaultConstructor)
var anObject = __webpack_require__(29);
var aFunction = __webpack_require__(101);
var SPECIES = __webpack_require__(21)('species');
module.exports = function (O, D) {
  var C = anObject(O).constructor;
  var S;
  return C === undefined || (S = anObject(C)[SPECIES]) == undefined ? D : aFunction(S);
};


/***/ }),
/* 283 */
/***/ (function(module, exports, __webpack_require__) {

var ctx = __webpack_require__(33);
var invoke = __webpack_require__(514);
var html = __webpack_require__(260);
var cel = __webpack_require__(176);
var global = __webpack_require__(17);
var process = global.process;
var setTask = global.setImmediate;
var clearTask = global.clearImmediate;
var MessageChannel = global.MessageChannel;
var Dispatch = global.Dispatch;
var counter = 0;
var queue = {};
var ONREADYSTATECHANGE = 'onreadystatechange';
var defer, channel, port;
var run = function () {
  var id = +this;
  // eslint-disable-next-line no-prototype-builtins
  if (queue.hasOwnProperty(id)) {
    var fn = queue[id];
    delete queue[id];
    fn();
  }
};
var listener = function (event) {
  run.call(event.data);
};
// Node.js 0.9+ & IE10+ has setImmediate, otherwise:
if (!setTask || !clearTask) {
  setTask = function setImmediate(fn) {
    var args = [];
    var i = 1;
    while (arguments.length > i) args.push(arguments[i++]);
    queue[++counter] = function () {
      // eslint-disable-next-line no-new-func
      invoke(typeof fn == 'function' ? fn : Function(fn), args);
    };
    defer(counter);
    return counter;
  };
  clearTask = function clearImmediate(id) {
    delete queue[id];
  };
  // Node.js 0.8-
  if (__webpack_require__(99)(process) == 'process') {
    defer = function (id) {
      process.nextTick(ctx(run, id, 1));
    };
  // Sphere (JS game engine) Dispatch API
  } else if (Dispatch && Dispatch.now) {
    defer = function (id) {
      Dispatch.now(ctx(run, id, 1));
    };
  // Browsers with MessageChannel, includes WebWorkers
  } else if (MessageChannel) {
    channel = new MessageChannel();
    port = channel.port2;
    channel.port1.onmessage = listener;
    defer = ctx(port.postMessage, port, 1);
  // Browsers with postMessage, skip WebWorkers
  // IE8 has postMessage, but it's sync & typeof its postMessage is 'object'
  } else if (global.addEventListener && typeof postMessage == 'function' && !global.importScripts) {
    defer = function (id) {
      global.postMessage(id + '', '*');
    };
    global.addEventListener('message', listener, false);
  // IE8-
  } else if (ONREADYSTATECHANGE in cel('script')) {
    defer = function (id) {
      html.appendChild(cel('script'))[ONREADYSTATECHANGE] = function () {
        html.removeChild(this);
        run.call(id);
      };
    };
  // Rest old browsers
  } else {
    defer = function (id) {
      setTimeout(ctx(run, id, 1), 0);
    };
  }
}
module.exports = {
  set: setTask,
  clear: clearTask
};


/***/ }),
/* 284 */
/***/ (function(module, exports) {

module.exports = function (exec) {
  try {
    return { e: false, v: exec() };
  } catch (e) {
    return { e: true, v: e };
  }
};


/***/ }),
/* 285 */
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__(29);
var isObject = __webpack_require__(22);
var newPromiseCapability = __webpack_require__(193);

module.exports = function (C, x) {
  anObject(C);
  if (isObject(x) && x.constructor === C) return x;
  var promiseCapability = newPromiseCapability.f(C);
  var resolve = promiseCapability.resolve;
  resolve(x);
  return promiseCapability.promise;
};


/***/ }),
/* 286 */
/***/ (function(module, exports, __webpack_require__) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/

var BrotliInput = __webpack_require__(287).BrotliInput;
var BrotliOutput = __webpack_require__(287).BrotliOutput;
var BrotliBitReader = __webpack_require__(520);
var BrotliDictionary = __webpack_require__(288);
var HuffmanCode = __webpack_require__(289).HuffmanCode;
var BrotliBuildHuffmanTable = __webpack_require__(289).BrotliBuildHuffmanTable;
var Context = __webpack_require__(524);
var Prefix = __webpack_require__(525);
var Transform = __webpack_require__(526);

var kDefaultCodeLength = 8;
var kCodeLengthRepeatCode = 16;
var kNumLiteralCodes = 256;
var kNumInsertAndCopyCodes = 704;
var kNumBlockLengthCodes = 26;
var kLiteralContextBits = 6;
var kDistanceContextBits = 2;

var HUFFMAN_TABLE_BITS = 8;
var HUFFMAN_TABLE_MASK = 0xff;
/* Maximum possible Huffman table size for an alphabet size of 704, max code
 * length 15 and root table bits 8. */
var HUFFMAN_MAX_TABLE_SIZE = 1080;

var CODE_LENGTH_CODES = 18;
var kCodeLengthCodeOrder = new Uint8Array([
  1, 2, 3, 4, 0, 5, 17, 6, 16, 7, 8, 9, 10, 11, 12, 13, 14, 15,
]);

var NUM_DISTANCE_SHORT_CODES = 16;
var kDistanceShortCodeIndexOffset = new Uint8Array([
  3, 2, 1, 0, 3, 3, 3, 3, 3, 3, 2, 2, 2, 2, 2, 2
]);

var kDistanceShortCodeValueOffset = new Int8Array([
  0, 0, 0, 0, -1, 1, -2, 2, -3, 3, -1, 1, -2, 2, -3, 3
]);

var kMaxHuffmanTableSize = new Uint16Array([
  256, 402, 436, 468, 500, 534, 566, 598, 630, 662, 694, 726, 758, 790, 822,
  854, 886, 920, 952, 984, 1016, 1048, 1080
]);

function DecodeWindowBits(br) {
  var n;
  if (br.readBits(1) === 0) {
    return 16;
  }
  
  n = br.readBits(3);
  if (n > 0) {
    return 17 + n;
  }
  
  n = br.readBits(3);
  if (n > 0) {
    return 8 + n;
  }
  
  return 17;
}

/* Decodes a number in the range [0..255], by reading 1 - 11 bits. */
function DecodeVarLenUint8(br) {
  if (br.readBits(1)) {
    var nbits = br.readBits(3);
    if (nbits === 0) {
      return 1;
    } else {
      return br.readBits(nbits) + (1 << nbits);
    }
  }
  return 0;
}

function MetaBlockLength() {
  this.meta_block_length = 0;
  this.input_end = 0;
  this.is_uncompressed = 0;
  this.is_metadata = false;
}

function DecodeMetaBlockLength(br) {
  var out = new MetaBlockLength;  
  var size_nibbles;
  var size_bytes;
  var i;
  
  out.input_end = br.readBits(1);
  if (out.input_end && br.readBits(1)) {
    return out;
  }
  
  size_nibbles = br.readBits(2) + 4;
  if (size_nibbles === 7) {
    out.is_metadata = true;
    
    if (br.readBits(1) !== 0)
      throw new Error('Invalid reserved bit');
    
    size_bytes = br.readBits(2);
    if (size_bytes === 0)
      return out;
    
    for (i = 0; i < size_bytes; i++) {
      var next_byte = br.readBits(8);
      if (i + 1 === size_bytes && size_bytes > 1 && next_byte === 0)
        throw new Error('Invalid size byte');
      
      out.meta_block_length |= next_byte << (i * 8);
    }
  } else {
    for (i = 0; i < size_nibbles; ++i) {
      var next_nibble = br.readBits(4);
      if (i + 1 === size_nibbles && size_nibbles > 4 && next_nibble === 0)
        throw new Error('Invalid size nibble');
      
      out.meta_block_length |= next_nibble << (i * 4);
    }
  }
  
  ++out.meta_block_length;
  
  if (!out.input_end && !out.is_metadata) {
    out.is_uncompressed = br.readBits(1);
  }
  
  return out;
}

/* Decodes the next Huffman code from bit-stream. */
function ReadSymbol(table, index, br) {
  var start_index = index;
  
  var nbits;
  br.fillBitWindow();
  index += (br.val_ >>> br.bit_pos_) & HUFFMAN_TABLE_MASK;
  nbits = table[index].bits - HUFFMAN_TABLE_BITS;
  if (nbits > 0) {
    br.bit_pos_ += HUFFMAN_TABLE_BITS;
    index += table[index].value;
    index += (br.val_ >>> br.bit_pos_) & ((1 << nbits) - 1);
  }
  br.bit_pos_ += table[index].bits;
  return table[index].value;
}

function ReadHuffmanCodeLengths(code_length_code_lengths, num_symbols, code_lengths, br) {
  var symbol = 0;
  var prev_code_len = kDefaultCodeLength;
  var repeat = 0;
  var repeat_code_len = 0;
  var space = 32768;
  
  var table = [];
  for (var i = 0; i < 32; i++)
    table.push(new HuffmanCode(0, 0));
  
  BrotliBuildHuffmanTable(table, 0, 5, code_length_code_lengths, CODE_LENGTH_CODES);

  while (symbol < num_symbols && space > 0) {
    var p = 0;
    var code_len;
    
    br.readMoreInput();
    br.fillBitWindow();
    p += (br.val_ >>> br.bit_pos_) & 31;
    br.bit_pos_ += table[p].bits;
    code_len = table[p].value & 0xff;
    if (code_len < kCodeLengthRepeatCode) {
      repeat = 0;
      code_lengths[symbol++] = code_len;
      if (code_len !== 0) {
        prev_code_len = code_len;
        space -= 32768 >> code_len;
      }
    } else {
      var extra_bits = code_len - 14;
      var old_repeat;
      var repeat_delta;
      var new_len = 0;
      if (code_len === kCodeLengthRepeatCode) {
        new_len = prev_code_len;
      }
      if (repeat_code_len !== new_len) {
        repeat = 0;
        repeat_code_len = new_len;
      }
      old_repeat = repeat;
      if (repeat > 0) {
        repeat -= 2;
        repeat <<= extra_bits;
      }
      repeat += br.readBits(extra_bits) + 3;
      repeat_delta = repeat - old_repeat;
      if (symbol + repeat_delta > num_symbols) {
        throw new Error('[ReadHuffmanCodeLengths] symbol + repeat_delta > num_symbols');
      }
      
      for (var x = 0; x < repeat_delta; x++)
        code_lengths[symbol + x] = repeat_code_len;
      
      symbol += repeat_delta;
      
      if (repeat_code_len !== 0) {
        space -= repeat_delta << (15 - repeat_code_len);
      }
    }
  }
  if (space !== 0) {
    throw new Error("[ReadHuffmanCodeLengths] space = " + space);
  }
  
  for (; symbol < num_symbols; symbol++)
    code_lengths[symbol] = 0;
}

function ReadHuffmanCode(alphabet_size, tables, table, br) {
  var table_size = 0;
  var simple_code_or_skip;
  var code_lengths = new Uint8Array(alphabet_size);
  
  br.readMoreInput();
  
  /* simple_code_or_skip is used as follows:
     1 for simple code;
     0 for no skipping, 2 skips 2 code lengths, 3 skips 3 code lengths */
  simple_code_or_skip = br.readBits(2);
  if (simple_code_or_skip === 1) {
    /* Read symbols, codes & code lengths directly. */
    var i;
    var max_bits_counter = alphabet_size - 1;
    var max_bits = 0;
    var symbols = new Int32Array(4);
    var num_symbols = br.readBits(2) + 1;
    while (max_bits_counter) {
      max_bits_counter >>= 1;
      ++max_bits;
    }

    for (i = 0; i < num_symbols; ++i) {
      symbols[i] = br.readBits(max_bits) % alphabet_size;
      code_lengths[symbols[i]] = 2;
    }
    code_lengths[symbols[0]] = 1;
    switch (num_symbols) {
      case 1:
        break;
      case 3:
        if ((symbols[0] === symbols[1]) ||
            (symbols[0] === symbols[2]) ||
            (symbols[1] === symbols[2])) {
          throw new Error('[ReadHuffmanCode] invalid symbols');
        }
        break;
      case 2:
        if (symbols[0] === symbols[1]) {
          throw new Error('[ReadHuffmanCode] invalid symbols');
        }
        
        code_lengths[symbols[1]] = 1;
        break;
      case 4:
        if ((symbols[0] === symbols[1]) ||
            (symbols[0] === symbols[2]) ||
            (symbols[0] === symbols[3]) ||
            (symbols[1] === symbols[2]) ||
            (symbols[1] === symbols[3]) ||
            (symbols[2] === symbols[3])) {
          throw new Error('[ReadHuffmanCode] invalid symbols');
        }
        
        if (br.readBits(1)) {
          code_lengths[symbols[2]] = 3;
          code_lengths[symbols[3]] = 3;
        } else {
          code_lengths[symbols[0]] = 2;
        }
        break;
    }
  } else {  /* Decode Huffman-coded code lengths. */
    var i;
    var code_length_code_lengths = new Uint8Array(CODE_LENGTH_CODES);
    var space = 32;
    var num_codes = 0;
    /* Static Huffman code for the code length code lengths */
    var huff = [
      new HuffmanCode(2, 0), new HuffmanCode(2, 4), new HuffmanCode(2, 3), new HuffmanCode(3, 2), 
      new HuffmanCode(2, 0), new HuffmanCode(2, 4), new HuffmanCode(2, 3), new HuffmanCode(4, 1),
      new HuffmanCode(2, 0), new HuffmanCode(2, 4), new HuffmanCode(2, 3), new HuffmanCode(3, 2), 
      new HuffmanCode(2, 0), new HuffmanCode(2, 4), new HuffmanCode(2, 3), new HuffmanCode(4, 5)
    ];
    for (i = simple_code_or_skip; i < CODE_LENGTH_CODES && space > 0; ++i) {
      var code_len_idx = kCodeLengthCodeOrder[i];
      var p = 0;
      var v;
      br.fillBitWindow();
      p += (br.val_ >>> br.bit_pos_) & 15;
      br.bit_pos_ += huff[p].bits;
      v = huff[p].value;
      code_length_code_lengths[code_len_idx] = v;
      if (v !== 0) {
        space -= (32 >> v);
        ++num_codes;
      }
    }
    
    if (!(num_codes === 1 || space === 0))
      throw new Error('[ReadHuffmanCode] invalid num_codes or space');
    
    ReadHuffmanCodeLengths(code_length_code_lengths, alphabet_size, code_lengths, br);
  }
  
  table_size = BrotliBuildHuffmanTable(tables, table, HUFFMAN_TABLE_BITS, code_lengths, alphabet_size);
  
  if (table_size === 0) {
    throw new Error("[ReadHuffmanCode] BuildHuffmanTable failed: ");
  }
  
  return table_size;
}

function ReadBlockLength(table, index, br) {
  var code;
  var nbits;
  code = ReadSymbol(table, index, br);
  nbits = Prefix.kBlockLengthPrefixCode[code].nbits;
  return Prefix.kBlockLengthPrefixCode[code].offset + br.readBits(nbits);
}

function TranslateShortCodes(code, ringbuffer, index) {
  var val;
  if (code < NUM_DISTANCE_SHORT_CODES) {
    index += kDistanceShortCodeIndexOffset[code];
    index &= 3;
    val = ringbuffer[index] + kDistanceShortCodeValueOffset[code];
  } else {
    val = code - NUM_DISTANCE_SHORT_CODES + 1;
  }
  return val;
}

function MoveToFront(v, index) {
  var value = v[index];
  var i = index;
  for (; i; --i) v[i] = v[i - 1];
  v[0] = value;
}

function InverseMoveToFrontTransform(v, v_len) {
  var mtf = new Uint8Array(256);
  var i;
  for (i = 0; i < 256; ++i) {
    mtf[i] = i;
  }
  for (i = 0; i < v_len; ++i) {
    var index = v[i];
    v[i] = mtf[index];
    if (index) MoveToFront(mtf, index);
  }
}

/* Contains a collection of huffman trees with the same alphabet size. */
function HuffmanTreeGroup(alphabet_size, num_htrees) {
  this.alphabet_size = alphabet_size;
  this.num_htrees = num_htrees;
  this.codes = new Array(num_htrees + num_htrees * kMaxHuffmanTableSize[(alphabet_size + 31) >>> 5]);  
  this.htrees = new Uint32Array(num_htrees);
}

HuffmanTreeGroup.prototype.decode = function(br) {
  var i;
  var table_size;
  var next = 0;
  for (i = 0; i < this.num_htrees; ++i) {
    this.htrees[i] = next;
    table_size = ReadHuffmanCode(this.alphabet_size, this.codes, next, br);
    next += table_size;
  }
};

function DecodeContextMap(context_map_size, br) {
  var out = { num_htrees: null, context_map: null };
  var use_rle_for_zeros;
  var max_run_length_prefix = 0;
  var table;
  var i;
  
  br.readMoreInput();
  var num_htrees = out.num_htrees = DecodeVarLenUint8(br) + 1;

  var context_map = out.context_map = new Uint8Array(context_map_size);
  if (num_htrees <= 1) {
    return out;
  }

  use_rle_for_zeros = br.readBits(1);
  if (use_rle_for_zeros) {
    max_run_length_prefix = br.readBits(4) + 1;
  }
  
  table = [];
  for (i = 0; i < HUFFMAN_MAX_TABLE_SIZE; i++) {
    table[i] = new HuffmanCode(0, 0);
  }
  
  ReadHuffmanCode(num_htrees + max_run_length_prefix, table, 0, br);
  
  for (i = 0; i < context_map_size;) {
    var code;

    br.readMoreInput();
    code = ReadSymbol(table, 0, br);
    if (code === 0) {
      context_map[i] = 0;
      ++i;
    } else if (code <= max_run_length_prefix) {
      var reps = 1 + (1 << code) + br.readBits(code);
      while (--reps) {
        if (i >= context_map_size) {
          throw new Error("[DecodeContextMap] i >= context_map_size");
        }
        context_map[i] = 0;
        ++i;
      }
    } else {
      context_map[i] = code - max_run_length_prefix;
      ++i;
    }
  }
  if (br.readBits(1)) {
    InverseMoveToFrontTransform(context_map, context_map_size);
  }
  
  return out;
}

function DecodeBlockType(max_block_type, trees, tree_type, block_types, ringbuffers, indexes, br) {
  var ringbuffer = tree_type * 2;
  var index = tree_type;
  var type_code = ReadSymbol(trees, tree_type * HUFFMAN_MAX_TABLE_SIZE, br);
  var block_type;
  if (type_code === 0) {
    block_type = ringbuffers[ringbuffer + (indexes[index] & 1)];
  } else if (type_code === 1) {
    block_type = ringbuffers[ringbuffer + ((indexes[index] - 1) & 1)] + 1;
  } else {
    block_type = type_code - 2;
  }
  if (block_type >= max_block_type) {
    block_type -= max_block_type;
  }
  block_types[tree_type] = block_type;
  ringbuffers[ringbuffer + (indexes[index] & 1)] = block_type;
  ++indexes[index];
}

function CopyUncompressedBlockToOutput(output, len, pos, ringbuffer, ringbuffer_mask, br) {
  var rb_size = ringbuffer_mask + 1;
  var rb_pos = pos & ringbuffer_mask;
  var br_pos = br.pos_ & BrotliBitReader.IBUF_MASK;
  var nbytes;

  /* For short lengths copy byte-by-byte */
  if (len < 8 || br.bit_pos_ + (len << 3) < br.bit_end_pos_) {
    while (len-- > 0) {
      br.readMoreInput();
      ringbuffer[rb_pos++] = br.readBits(8);
      if (rb_pos === rb_size) {
        output.write(ringbuffer, rb_size);
        rb_pos = 0;
      }
    }
    return;
  }

  if (br.bit_end_pos_ < 32) {
    throw new Error('[CopyUncompressedBlockToOutput] br.bit_end_pos_ < 32');
  }

  /* Copy remaining 0-4 bytes from br.val_ to ringbuffer. */
  while (br.bit_pos_ < 32) {
    ringbuffer[rb_pos] = (br.val_ >>> br.bit_pos_);
    br.bit_pos_ += 8;
    ++rb_pos;
    --len;
  }

  /* Copy remaining bytes from br.buf_ to ringbuffer. */
  nbytes = (br.bit_end_pos_ - br.bit_pos_) >> 3;
  if (br_pos + nbytes > BrotliBitReader.IBUF_MASK) {
    var tail = BrotliBitReader.IBUF_MASK + 1 - br_pos;
    for (var x = 0; x < tail; x++)
      ringbuffer[rb_pos + x] = br.buf_[br_pos + x];
    
    nbytes -= tail;
    rb_pos += tail;
    len -= tail;
    br_pos = 0;
  }

  for (var x = 0; x < nbytes; x++)
    ringbuffer[rb_pos + x] = br.buf_[br_pos + x];
  
  rb_pos += nbytes;
  len -= nbytes;

  /* If we wrote past the logical end of the ringbuffer, copy the tail of the
     ringbuffer to its beginning and flush the ringbuffer to the output. */
  if (rb_pos >= rb_size) {
    output.write(ringbuffer, rb_size);
    rb_pos -= rb_size;    
    for (var x = 0; x < rb_pos; x++)
      ringbuffer[x] = ringbuffer[rb_size + x];
  }

  /* If we have more to copy than the remaining size of the ringbuffer, then we
     first fill the ringbuffer from the input and then flush the ringbuffer to
     the output */
  while (rb_pos + len >= rb_size) {
    nbytes = rb_size - rb_pos;
    if (br.input_.read(ringbuffer, rb_pos, nbytes) < nbytes) {
      throw new Error('[CopyUncompressedBlockToOutput] not enough bytes');
    }
    output.write(ringbuffer, rb_size);
    len -= nbytes;
    rb_pos = 0;
  }

  /* Copy straight from the input onto the ringbuffer. The ringbuffer will be
     flushed to the output at a later time. */
  if (br.input_.read(ringbuffer, rb_pos, len) < len) {
    throw new Error('[CopyUncompressedBlockToOutput] not enough bytes');
  }

  /* Restore the state of the bit reader. */
  br.reset();
}

/* Advances the bit reader position to the next byte boundary and verifies
   that any skipped bits are set to zero. */
function JumpToByteBoundary(br) {
  var new_bit_pos = (br.bit_pos_ + 7) & ~7;
  var pad_bits = br.readBits(new_bit_pos - br.bit_pos_);
  return pad_bits == 0;
}

function BrotliDecompressedSize(buffer) {
  var input = new BrotliInput(buffer);
  var br = new BrotliBitReader(input);
  DecodeWindowBits(br);
  var out = DecodeMetaBlockLength(br);
  return out.meta_block_length;
}

exports.BrotliDecompressedSize = BrotliDecompressedSize;

function BrotliDecompressBuffer(buffer, output_size) {
  var input = new BrotliInput(buffer);
  
  if (output_size == null) {
    output_size = BrotliDecompressedSize(buffer);
  }
  
  var output_buffer = new Uint8Array(output_size);
  var output = new BrotliOutput(output_buffer);
  
  BrotliDecompress(input, output);
  
  if (output.pos < output.buffer.length) {
    output.buffer = output.buffer.subarray(0, output.pos);
  }
  
  return output.buffer;
}

exports.BrotliDecompressBuffer = BrotliDecompressBuffer;

function BrotliDecompress(input, output) {
  var i;
  var pos = 0;
  var input_end = 0;
  var window_bits = 0;
  var max_backward_distance;
  var max_distance = 0;
  var ringbuffer_size;
  var ringbuffer_mask;
  var ringbuffer;
  var ringbuffer_end;
  /* This ring buffer holds a few past copy distances that will be used by */
  /* some special distance codes. */
  var dist_rb = [ 16, 15, 11, 4 ];
  var dist_rb_idx = 0;
  /* The previous 2 bytes used for context. */
  var prev_byte1 = 0;
  var prev_byte2 = 0;
  var hgroup = [new HuffmanTreeGroup(0, 0), new HuffmanTreeGroup(0, 0), new HuffmanTreeGroup(0, 0)];
  var block_type_trees;
  var block_len_trees;
  var br;

  /* We need the slack region for the following reasons:
       - always doing two 8-byte copies for fast backward copying
       - transforms
       - flushing the input ringbuffer when decoding uncompressed blocks */
  var kRingBufferWriteAheadSlack = 128 + BrotliBitReader.READ_SIZE;

  br = new BrotliBitReader(input);

  /* Decode window size. */
  window_bits = DecodeWindowBits(br);
  max_backward_distance = (1 << window_bits) - 16;

  ringbuffer_size = 1 << window_bits;
  ringbuffer_mask = ringbuffer_size - 1;
  ringbuffer = new Uint8Array(ringbuffer_size + kRingBufferWriteAheadSlack + BrotliDictionary.maxDictionaryWordLength);
  ringbuffer_end = ringbuffer_size;

  block_type_trees = [];
  block_len_trees = [];
  for (var x = 0; x < 3 * HUFFMAN_MAX_TABLE_SIZE; x++) {
    block_type_trees[x] = new HuffmanCode(0, 0);
    block_len_trees[x] = new HuffmanCode(0, 0);
  }

  while (!input_end) {
    var meta_block_remaining_len = 0;
    var is_uncompressed;
    var block_length = [ 1 << 28, 1 << 28, 1 << 28 ];
    var block_type = [ 0 ];
    var num_block_types = [ 1, 1, 1 ];
    var block_type_rb = [ 0, 1, 0, 1, 0, 1 ];
    var block_type_rb_index = [ 0 ];
    var distance_postfix_bits;
    var num_direct_distance_codes;
    var distance_postfix_mask;
    var num_distance_codes;
    var context_map = null;
    var context_modes = null;
    var num_literal_htrees;
    var dist_context_map = null;
    var num_dist_htrees;
    var context_offset = 0;
    var context_map_slice = null;
    var literal_htree_index = 0;
    var dist_context_offset = 0;
    var dist_context_map_slice = null;
    var dist_htree_index = 0;
    var context_lookup_offset1 = 0;
    var context_lookup_offset2 = 0;
    var context_mode;
    var htree_command;

    for (i = 0; i < 3; ++i) {
      hgroup[i].codes = null;
      hgroup[i].htrees = null;
    }

    br.readMoreInput();
    
    var _out = DecodeMetaBlockLength(br);
    meta_block_remaining_len = _out.meta_block_length;
    if (pos + meta_block_remaining_len > output.buffer.length) {
      /* We need to grow the output buffer to fit the additional data. */
      var tmp = new Uint8Array( pos + meta_block_remaining_len );
      tmp.set( output.buffer );
      output.buffer = tmp;
    }    
    input_end = _out.input_end;
    is_uncompressed = _out.is_uncompressed;
    
    if (_out.is_metadata) {
      JumpToByteBoundary(br);
      
      for (; meta_block_remaining_len > 0; --meta_block_remaining_len) {
        br.readMoreInput();
        /* Read one byte and ignore it. */
        br.readBits(8);
      }
      
      continue;
    }
    
    if (meta_block_remaining_len === 0) {
      continue;
    }
    
    if (is_uncompressed) {
      br.bit_pos_ = (br.bit_pos_ + 7) & ~7;
      CopyUncompressedBlockToOutput(output, meta_block_remaining_len, pos,
                                    ringbuffer, ringbuffer_mask, br);
      pos += meta_block_remaining_len;
      continue;
    }
    
    for (i = 0; i < 3; ++i) {
      num_block_types[i] = DecodeVarLenUint8(br) + 1;
      if (num_block_types[i] >= 2) {
        ReadHuffmanCode(num_block_types[i] + 2, block_type_trees, i * HUFFMAN_MAX_TABLE_SIZE, br);
        ReadHuffmanCode(kNumBlockLengthCodes, block_len_trees, i * HUFFMAN_MAX_TABLE_SIZE, br);
        block_length[i] = ReadBlockLength(block_len_trees, i * HUFFMAN_MAX_TABLE_SIZE, br);
        block_type_rb_index[i] = 1;
      }
    }
    
    br.readMoreInput();
    
    distance_postfix_bits = br.readBits(2);
    num_direct_distance_codes = NUM_DISTANCE_SHORT_CODES + (br.readBits(4) << distance_postfix_bits);
    distance_postfix_mask = (1 << distance_postfix_bits) - 1;
    num_distance_codes = (num_direct_distance_codes + (48 << distance_postfix_bits));
    context_modes = new Uint8Array(num_block_types[0]);

    for (i = 0; i < num_block_types[0]; ++i) {
       br.readMoreInput();
       context_modes[i] = (br.readBits(2) << 1);
    }
    
    var _o1 = DecodeContextMap(num_block_types[0] << kLiteralContextBits, br);
    num_literal_htrees = _o1.num_htrees;
    context_map = _o1.context_map;
    
    var _o2 = DecodeContextMap(num_block_types[2] << kDistanceContextBits, br);
    num_dist_htrees = _o2.num_htrees;
    dist_context_map = _o2.context_map;
    
    hgroup[0] = new HuffmanTreeGroup(kNumLiteralCodes, num_literal_htrees);
    hgroup[1] = new HuffmanTreeGroup(kNumInsertAndCopyCodes, num_block_types[1]);
    hgroup[2] = new HuffmanTreeGroup(num_distance_codes, num_dist_htrees);

    for (i = 0; i < 3; ++i) {
      hgroup[i].decode(br);
    }

    context_map_slice = 0;
    dist_context_map_slice = 0;
    context_mode = context_modes[block_type[0]];
    context_lookup_offset1 = Context.lookupOffsets[context_mode];
    context_lookup_offset2 = Context.lookupOffsets[context_mode + 1];
    htree_command = hgroup[1].htrees[0];

    while (meta_block_remaining_len > 0) {
      var cmd_code;
      var range_idx;
      var insert_code;
      var copy_code;
      var insert_length;
      var copy_length;
      var distance_code;
      var distance;
      var context;
      var j;
      var copy_dst;

      br.readMoreInput();
      
      if (block_length[1] === 0) {
        DecodeBlockType(num_block_types[1],
                        block_type_trees, 1, block_type, block_type_rb,
                        block_type_rb_index, br);
        block_length[1] = ReadBlockLength(block_len_trees, HUFFMAN_MAX_TABLE_SIZE, br);
        htree_command = hgroup[1].htrees[block_type[1]];
      }
      --block_length[1];
      cmd_code = ReadSymbol(hgroup[1].codes, htree_command, br);
      range_idx = cmd_code >> 6;
      if (range_idx >= 2) {
        range_idx -= 2;
        distance_code = -1;
      } else {
        distance_code = 0;
      }
      insert_code = Prefix.kInsertRangeLut[range_idx] + ((cmd_code >> 3) & 7);
      copy_code = Prefix.kCopyRangeLut[range_idx] + (cmd_code & 7);
      insert_length = Prefix.kInsertLengthPrefixCode[insert_code].offset +
          br.readBits(Prefix.kInsertLengthPrefixCode[insert_code].nbits);
      copy_length = Prefix.kCopyLengthPrefixCode[copy_code].offset +
          br.readBits(Prefix.kCopyLengthPrefixCode[copy_code].nbits);
      prev_byte1 = ringbuffer[pos-1 & ringbuffer_mask];
      prev_byte2 = ringbuffer[pos-2 & ringbuffer_mask];
      for (j = 0; j < insert_length; ++j) {
        br.readMoreInput();

        if (block_length[0] === 0) {
          DecodeBlockType(num_block_types[0],
                          block_type_trees, 0, block_type, block_type_rb,
                          block_type_rb_index, br);
          block_length[0] = ReadBlockLength(block_len_trees, 0, br);
          context_offset = block_type[0] << kLiteralContextBits;
          context_map_slice = context_offset;
          context_mode = context_modes[block_type[0]];
          context_lookup_offset1 = Context.lookupOffsets[context_mode];
          context_lookup_offset2 = Context.lookupOffsets[context_mode + 1];
        }
        context = (Context.lookup[context_lookup_offset1 + prev_byte1] |
                   Context.lookup[context_lookup_offset2 + prev_byte2]);
        literal_htree_index = context_map[context_map_slice + context];
        --block_length[0];
        prev_byte2 = prev_byte1;
        prev_byte1 = ReadSymbol(hgroup[0].codes, hgroup[0].htrees[literal_htree_index], br);
        ringbuffer[pos & ringbuffer_mask] = prev_byte1;
        if ((pos & ringbuffer_mask) === ringbuffer_mask) {
          output.write(ringbuffer, ringbuffer_size);
        }
        ++pos;
      }
      meta_block_remaining_len -= insert_length;
      if (meta_block_remaining_len <= 0) break;

      if (distance_code < 0) {
        var context;
        
        br.readMoreInput();
        if (block_length[2] === 0) {
          DecodeBlockType(num_block_types[2],
                          block_type_trees, 2, block_type, block_type_rb,
                          block_type_rb_index, br);
          block_length[2] = ReadBlockLength(block_len_trees, 2 * HUFFMAN_MAX_TABLE_SIZE, br);
          dist_context_offset = block_type[2] << kDistanceContextBits;
          dist_context_map_slice = dist_context_offset;
        }
        --block_length[2];
        context = (copy_length > 4 ? 3 : copy_length - 2) & 0xff;
        dist_htree_index = dist_context_map[dist_context_map_slice + context];
        distance_code = ReadSymbol(hgroup[2].codes, hgroup[2].htrees[dist_htree_index], br);
        if (distance_code >= num_direct_distance_codes) {
          var nbits;
          var postfix;
          var offset;
          distance_code -= num_direct_distance_codes;
          postfix = distance_code & distance_postfix_mask;
          distance_code >>= distance_postfix_bits;
          nbits = (distance_code >> 1) + 1;
          offset = ((2 + (distance_code & 1)) << nbits) - 4;
          distance_code = num_direct_distance_codes +
              ((offset + br.readBits(nbits)) <<
               distance_postfix_bits) + postfix;
        }
      }

      /* Convert the distance code to the actual distance by possibly looking */
      /* up past distnaces from the ringbuffer. */
      distance = TranslateShortCodes(distance_code, dist_rb, dist_rb_idx);
      if (distance < 0) {
        throw new Error('[BrotliDecompress] invalid distance');
      }

      if (pos < max_backward_distance &&
          max_distance !== max_backward_distance) {
        max_distance = pos;
      } else {
        max_distance = max_backward_distance;
      }

      copy_dst = pos & ringbuffer_mask;

      if (distance > max_distance) {
        if (copy_length >= BrotliDictionary.minDictionaryWordLength &&
            copy_length <= BrotliDictionary.maxDictionaryWordLength) {
          var offset = BrotliDictionary.offsetsByLength[copy_length];
          var word_id = distance - max_distance - 1;
          var shift = BrotliDictionary.sizeBitsByLength[copy_length];
          var mask = (1 << shift) - 1;
          var word_idx = word_id & mask;
          var transform_idx = word_id >> shift;
          offset += word_idx * copy_length;
          if (transform_idx < Transform.kNumTransforms) {
            var len = Transform.transformDictionaryWord(ringbuffer, copy_dst, offset, copy_length, transform_idx);
            copy_dst += len;
            pos += len;
            meta_block_remaining_len -= len;
            if (copy_dst >= ringbuffer_end) {
              output.write(ringbuffer, ringbuffer_size);
              
              for (var _x = 0; _x < (copy_dst - ringbuffer_end); _x++)
                ringbuffer[_x] = ringbuffer[ringbuffer_end + _x];
            }
          } else {
            throw new Error("Invalid backward reference. pos: " + pos + " distance: " + distance +
              " len: " + copy_length + " bytes left: " + meta_block_remaining_len);
          }
        } else {
          throw new Error("Invalid backward reference. pos: " + pos + " distance: " + distance +
            " len: " + copy_length + " bytes left: " + meta_block_remaining_len);
        }
      } else {
        if (distance_code > 0) {
          dist_rb[dist_rb_idx & 3] = distance;
          ++dist_rb_idx;
        }

        if (copy_length > meta_block_remaining_len) {
          throw new Error("Invalid backward reference. pos: " + pos + " distance: " + distance +
            " len: " + copy_length + " bytes left: " + meta_block_remaining_len);
        }

        for (j = 0; j < copy_length; ++j) {
          ringbuffer[pos & ringbuffer_mask] = ringbuffer[(pos - distance) & ringbuffer_mask];
          if ((pos & ringbuffer_mask) === ringbuffer_mask) {
            output.write(ringbuffer, ringbuffer_size);
          }
          ++pos;
          --meta_block_remaining_len;
        }
      }

      /* When we get here, we must have inserted at least one literal and */
      /* made a copy of at least length two, therefore accessing the last 2 */
      /* bytes is valid. */
      prev_byte1 = ringbuffer[(pos - 1) & ringbuffer_mask];
      prev_byte2 = ringbuffer[(pos - 2) & ringbuffer_mask];
    }

    /* Protect pos from overflow, wrap it around at every GB of input data */
    pos &= 0x3fffffff;
  }

  output.write(ringbuffer, pos & ringbuffer_mask);
}

exports.BrotliDecompress = BrotliDecompress;

BrotliDictionary.init();


/***/ }),
/* 287 */
/***/ (function(module, exports) {

function BrotliInput(buffer) {
  this.buffer = buffer;
  this.pos = 0;
}

BrotliInput.prototype.read = function(buf, i, count) {
  if (this.pos + count > this.buffer.length) {
    count = this.buffer.length - this.pos;
  }
  
  for (var p = 0; p < count; p++)
    buf[i + p] = this.buffer[this.pos + p];
  
  this.pos += count;
  return count;
}

exports.BrotliInput = BrotliInput;

function BrotliOutput(buf) {
  this.buffer = buf;
  this.pos = 0;
}

BrotliOutput.prototype.write = function(buf, count) {
  if (this.pos + count > this.buffer.length)
    throw new Error('Output buffer is not large enough');
  
  this.buffer.set(buf.subarray(0, count), this.pos);
  this.pos += count;
  return count;
};

exports.BrotliOutput = BrotliOutput;


/***/ }),
/* 288 */
/***/ (function(module, exports, __webpack_require__) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.

   Collection of static dictionary words.
*/

var data = __webpack_require__(521);
exports.init = function() {
  exports.dictionary = data.init();
};

exports.offsetsByLength = new Uint32Array([
     0,     0,     0,     0,     0,  4096,  9216, 21504, 35840, 44032,
 53248, 63488, 74752, 87040, 93696, 100864, 104704, 106752, 108928, 113536,
 115968, 118528, 119872, 121280, 122016,
]);

exports.sizeBitsByLength = new Uint8Array([
  0,  0,  0,  0, 10, 10, 11, 11, 10, 10,
 10, 10, 10,  9,  9,  8,  7,  7,  8,  7,
  7,  6,  6,  5,  5,
]);

exports.minDictionaryWordLength = 4;
exports.maxDictionaryWordLength = 24;


/***/ }),
/* 289 */
/***/ (function(module, exports) {

function HuffmanCode(bits, value) {
  this.bits = bits;   /* number of bits used for this symbol */
  this.value = value; /* symbol value or table offset */
}

exports.HuffmanCode = HuffmanCode;

var MAX_LENGTH = 15;

/* Returns reverse(reverse(key, len) + 1, len), where reverse(key, len) is the
   bit-wise reversal of the len least significant bits of key. */
function GetNextKey(key, len) {
  var step = 1 << (len - 1);
  while (key & step) {
    step >>= 1;
  }
  return (key & (step - 1)) + step;
}

/* Stores code in table[0], table[step], table[2*step], ..., table[end] */
/* Assumes that end is an integer multiple of step */
function ReplicateValue(table, i, step, end, code) {
  do {
    end -= step;
    table[i + end] = new HuffmanCode(code.bits, code.value);
  } while (end > 0);
}

/* Returns the table width of the next 2nd level table. count is the histogram
   of bit lengths for the remaining symbols, len is the code length of the next
   processed symbol */
function NextTableBitSize(count, len, root_bits) {
  var left = 1 << (len - root_bits);
  while (len < MAX_LENGTH) {
    left -= count[len];
    if (left <= 0) break;
    ++len;
    left <<= 1;
  }
  return len - root_bits;
}

exports.BrotliBuildHuffmanTable = function(root_table, table, root_bits, code_lengths, code_lengths_size) {
  var start_table = table;
  var code;            /* current table entry */
  var len;             /* current code length */
  var symbol;          /* symbol index in original or sorted table */
  var key;             /* reversed prefix code */
  var step;            /* step size to replicate values in current table */
  var low;             /* low bits for current root entry */
  var mask;            /* mask for low bits */
  var table_bits;      /* key length of current table */
  var table_size;      /* size of current table */
  var total_size;      /* sum of root table size and 2nd level table sizes */
  var sorted;          /* symbols sorted by code length */
  var count = new Int32Array(MAX_LENGTH + 1);  /* number of codes of each length */
  var offset = new Int32Array(MAX_LENGTH + 1);  /* offsets in sorted table for each length */

  sorted = new Int32Array(code_lengths_size);

  /* build histogram of code lengths */
  for (symbol = 0; symbol < code_lengths_size; symbol++) {
    count[code_lengths[symbol]]++;
  }

  /* generate offsets into sorted symbol table by code length */
  offset[1] = 0;
  for (len = 1; len < MAX_LENGTH; len++) {
    offset[len + 1] = offset[len] + count[len];
  }

  /* sort symbols by length, by symbol order within each length */
  for (symbol = 0; symbol < code_lengths_size; symbol++) {
    if (code_lengths[symbol] !== 0) {
      sorted[offset[code_lengths[symbol]]++] = symbol;
    }
  }
  
  table_bits = root_bits;
  table_size = 1 << table_bits;
  total_size = table_size;

  /* special case code with only one value */
  if (offset[MAX_LENGTH] === 1) {
    for (key = 0; key < total_size; ++key) {
      root_table[table + key] = new HuffmanCode(0, sorted[0] & 0xffff);
    }
    
    return total_size;
  }

  /* fill in root table */
  key = 0;
  symbol = 0;
  for (len = 1, step = 2; len <= root_bits; ++len, step <<= 1) {
    for (; count[len] > 0; --count[len]) {
      code = new HuffmanCode(len & 0xff, sorted[symbol++] & 0xffff);
      ReplicateValue(root_table, table + key, step, table_size, code);
      key = GetNextKey(key, len);
    }
  }

  /* fill in 2nd level tables and add pointers to root table */
  mask = total_size - 1;
  low = -1;
  for (len = root_bits + 1, step = 2; len <= MAX_LENGTH; ++len, step <<= 1) {
    for (; count[len] > 0; --count[len]) {
      if ((key & mask) !== low) {
        table += table_size;
        table_bits = NextTableBitSize(count, len, root_bits);
        table_size = 1 << table_bits;
        total_size += table_size;
        low = key & mask;
        root_table[start_table + low] = new HuffmanCode((table_bits + root_bits) & 0xff, ((table - start_table) - low) & 0xffff);
      }
      code = new HuffmanCode((len - root_bits) & 0xff, sorted[symbol++] & 0xffff);
      ReplicateValue(root_table, table + (key >> root_bits), step, table_size, code);
      key = GetNextKey(key, len);
    }
  }
  
  return total_size;
}


/***/ }),
/* 290 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var AI, AL, BA, BK, CB, CJ, CR, ID, LF, NL, NS, SA, SG, SP, WJ, XX;

var UnicodeTrie = __webpack_require__(527);



var base64 = __webpack_require__(528);

var _require = __webpack_require__(529);

BK = _require.BK;
CR = _require.CR;
LF = _require.LF;
NL = _require.NL;
CB = _require.CB;
BA = _require.BA;
SP = _require.SP;
WJ = _require.WJ;
SP = _require.SP;
BK = _require.BK;
LF = _require.LF;
NL = _require.NL;
AI = _require.AI;
AL = _require.AL;
SA = _require.SA;
SG = _require.SG;
XX = _require.XX;
CJ = _require.CJ;
ID = _require.ID;
NS = _require.NS;

var _require2 = __webpack_require__(530),
    DI_BRK = _require2.DI_BRK,
    IN_BRK = _require2.IN_BRK,
    CI_BRK = _require2.CI_BRK,
    CP_BRK = _require2.CP_BRK,
    PR_BRK = _require2.PR_BRK,
    pairTable = _require2.pairTable;

var data = base64.toByteArray("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");
var classTrie = new UnicodeTrie(data);

var mapClass = function mapClass(c) {
  switch (c) {
    case AI:
      return AL;

    case SA:
    case SG:
    case XX:
      return AL;

    case CJ:
      return NS;

    default:
      return c;
  }
};

var mapFirst = function mapFirst(c) {
  switch (c) {
    case LF:
    case NL:
      return BK;

    case CB:
      return BA;

    case SP:
      return WJ;

    default:
      return c;
  }
};

var Break = function Break(position, required) {
  if (required === void 0) {
    required = false;
  }

  this.position = position;
  this.required = required;
};

;

var LineBreaker = /*#__PURE__*/function () {
  function LineBreaker(string) {
    this.string = string;
    this.pos = 0;
    this.lastPos = 0;
    this.curClass = null;
    this.nextClass = null;
  }

  var _proto = LineBreaker.prototype;

  _proto.nextCodePoint = function nextCodePoint() {
    var code = this.string.charCodeAt(this.pos++);
    var next = this.string.charCodeAt(this.pos); // If a surrogate pair

    if (0xd800 <= code && code <= 0xdbff && 0xdc00 <= next && next <= 0xdfff) {
      this.pos++;
      return (code - 0xd800) * 0x400 + (next - 0xdc00) + 0x10000;
    }

    return code;
  };

  _proto.nextCharClass = function nextCharClass() {
    return mapClass(classTrie.get(this.nextCodePoint()));
  };

  _proto.nextBreak = function nextBreak() {
    // get the first char if we're at the beginning of the string
    if (this.curClass == null) {
      this.curClass = mapFirst(this.nextCharClass());
    }

    while (this.pos < this.string.length) {
      this.lastPos = this.pos;
      var lastClass = this.nextClass;
      this.nextClass = this.nextCharClass(); // explicit newline

      if (this.curClass === BK || this.curClass === CR && this.nextClass !== LF) {
        this.curClass = mapFirst(mapClass(this.nextClass));
        return new Break(this.lastPos, true);
      } // handle classes not handled by the pair table


      var cur = void 0;

      switch (this.nextClass) {
        case SP:
          cur = this.curClass;
          break;

        case BK:
        case LF:
        case NL:
          cur = BK;
          break;

        case CR:
          cur = CR;
          break;

        case CB:
          cur = BA;
          break;
      }

      if (cur != null) {
        this.curClass = cur;

        if (this.nextClass === CB) {
          return new Break(this.lastPos);
        }

        continue;
      } // if not handled already, use the pair table


      var shouldBreak = false;

      switch (pairTable[this.curClass][this.nextClass]) {
        case DI_BRK:
          // Direct break
          shouldBreak = true;
          break;

        case IN_BRK:
          // possible indirect break
          shouldBreak = lastClass === SP;
          break;

        case CI_BRK:
          shouldBreak = lastClass === SP;

          if (!shouldBreak) {
            continue;
          }

          break;

        case CP_BRK:
          // prohibited for combining marks
          if (lastClass !== SP) {
            continue;
          }

          break;
      }

      this.curClass = this.nextClass;

      if (shouldBreak) {
        return new Break(this.lastPos);
      }
    }

    if (this.pos >= this.string.length) {
      if (this.lastPos < this.string.length) {
        this.lastPos = this.string.length;
        return new Break(this.string.length);
      } else {
        return null;
      }
    }
  };

  return LineBreaker;
}();

;
module.exports = LineBreaker;

/***/ }),
/* 291 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


function TraversalTracker() {
	this.events = {};
}

TraversalTracker.prototype.startTracking = function (event, callback) {
	var callbacks = this.events[event] || (this.events[event] = []);

	if (callbacks.indexOf(callback) < 0) {
		callbacks.push(callback);
	}
};

TraversalTracker.prototype.stopTracking = function (event, callback) {
	var callbacks = this.events[event];

	if (!callbacks) {
		return;
	}

	var index = callbacks.indexOf(callback);
	if (index >= 0) {
		callbacks.splice(index, 1);
	}
};

TraversalTracker.prototype.emit = function (event) {
	var args = Array.prototype.slice.call(arguments, 1);
	var callbacks = this.events[event];

	if (!callbacks) {
		return;
	}

	callbacks.forEach(function (callback) {
		callback.apply(this, args);
	});
};

TraversalTracker.prototype.auto = function (event, callback, innerFunction) {
	this.startTracking(event, callback);
	innerFunction();
	this.stopTracking(event, callback);
};

module.exports = TraversalTracker;


/***/ }),
/* 292 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var isString = __webpack_require__(0).isString;
var isArray = __webpack_require__(0).isArray;
var isUndefined = __webpack_require__(0).isUndefined;
var isNull = __webpack_require__(0).isNull;

/**
 * Creates an instance of StyleContextStack used for style inheritance and style overrides
 *
 * @constructor
 * @this {StyleContextStack}
 * @param {Object} named styles dictionary
 * @param {Object} optional default style definition
 */
function StyleContextStack(styleDictionary, defaultStyle) {
	this.defaultStyle = defaultStyle || {};
	this.styleDictionary = styleDictionary;
	this.styleOverrides = [];
}

/**
 * Creates cloned version of current stack
 * @return {StyleContextStack} current stack snapshot
 */
StyleContextStack.prototype.clone = function () {
	var stack = new StyleContextStack(this.styleDictionary, this.defaultStyle);

	this.styleOverrides.forEach(function (item) {
		stack.styleOverrides.push(item);
	});

	return stack;
};

/**
 * Pushes style-name or style-overrides-object onto the stack for future evaluation
 *
 * @param {String|Object} styleNameOrOverride style-name (referring to styleDictionary) or
 *                                            a new dictionary defining overriding properties
 */
StyleContextStack.prototype.push = function (styleNameOrOverride) {
	this.styleOverrides.push(styleNameOrOverride);
};

/**
 * Removes last style-name or style-overrides-object from the stack
 *
 * @param {Number} howMany - optional number of elements to be popped (if not specified,
 *                           one element will be removed from the stack)
 */
StyleContextStack.prototype.pop = function (howMany) {
	howMany = howMany || 1;

	while (howMany-- > 0) {
		this.styleOverrides.pop();
	}
};

/**
 * Creates a set of named styles or/and a style-overrides-object based on the item,
 * pushes those elements onto the stack for future evaluation and returns the number
 * of elements pushed, so they can be easily poped then.
 *
 * @param {Object} item - an object with optional style property and/or style overrides
 * @return the number of items pushed onto the stack
 */
StyleContextStack.prototype.autopush = function (item) {
	if (isString(item)) {
		return 0;
	}

	var styleNames = [];

	if (item.style) {
		if (isArray(item.style)) {
			styleNames = item.style;
		} else {
			styleNames = [item.style];
		}
	}

	for (var i = 0, l = styleNames.length; i < l; i++) {
		this.push(styleNames[i]);
	}

	var styleProperties = [
		'font',
		'fontSize',
		'fontFeatures',
		'bold',
		'italics',
		'alignment',
		'color',
		'columnGap',
		'fillColor',
		'fillOpacity',
		'decoration',
		'decorationStyle',
		'decorationColor',
		'background',
		'lineHeight',
		'characterSpacing',
		'noWrap',
		'markerColor',
		'leadingIndent'
		//'tableCellPadding'
		// 'cellBorder',
		// 'headerCellBorder',
		// 'oddRowCellBorder',
		// 'evenRowCellBorder',
		// 'tableBorder'
	];
	var styleOverrideObject = {};
	var pushStyleOverrideObject = false;

	styleProperties.forEach(function (key) {
		if (!isUndefined(item[key]) && !isNull(item[key])) {
			styleOverrideObject[key] = item[key];
			pushStyleOverrideObject = true;
		}
	});

	if (pushStyleOverrideObject) {
		this.push(styleOverrideObject);
	}

	return styleNames.length + (pushStyleOverrideObject ? 1 : 0);
};

/**
 * Automatically pushes elements onto the stack, using autopush based on item,
 * executes callback and then pops elements back. Returns value returned by callback
 *
 * @param  {Object}   item - an object with optional style property and/or style overrides
 * @param  {Function} function to be called between autopush and pop
 * @return {Object} value returned by callback
 */
StyleContextStack.prototype.auto = function (item, callback) {
	var pushedItems = this.autopush(item);
	var result = callback();

	if (pushedItems > 0) {
		this.pop(pushedItems);
	}

	return result;
};

/**
 * Evaluates stack and returns value of a named property
 *
 * @param {String} property - property name
 * @return property value or null if not found
 */
StyleContextStack.prototype.getProperty = function (property) {
	if (this.styleOverrides) {
		for (var i = this.styleOverrides.length - 1; i >= 0; i--) {
			var item = this.styleOverrides[i];

			if (isString(item)) {
				// named-style-override
				var style = this.styleDictionary[item];
				if (style && !isUndefined(style[property]) && !isNull(style[property])) {
					return style[property];
				}
			} else if (!isUndefined(item[property]) && !isNull(item[property])) {
				// style-overrides-object
				return item[property];
			}
		}
	}

	return this.defaultStyle && this.defaultStyle[property];
};

module.exports = StyleContextStack;


/***/ }),
/* 293 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var TraversalTracker = __webpack_require__(291);
var isString = __webpack_require__(0).isString;

/**
 * Creates an instance of DocumentContext - a store for current x, y positions and available width/height.
 * It facilitates column divisions and vertical sync
 */
function DocumentContext(pageSize, pageMargins) {
	this.pages = [];

	this.pageMargins = pageMargins;

	this.x = pageMargins.left;
	this.availableWidth = pageSize.width - pageMargins.left - pageMargins.right;
	this.availableHeight = 0;
	this.page = -1;

	this.snapshots = [];

	this.endingCell = null;

	this.tracker = new TraversalTracker();

	this.backgroundLength = [];

	this.addPage(pageSize);
}

DocumentContext.prototype.beginColumnGroup = function () {
	this.snapshots.push({
		x: this.x,
		y: this.y,
		availableHeight: this.availableHeight,
		availableWidth: this.availableWidth,
		page: this.page,
		bottomMost: {
			x: this.x,
			y: this.y,
			availableHeight: this.availableHeight,
			availableWidth: this.availableWidth,
			page: this.page
		},
		endingCell: this.endingCell,
		lastColumnWidth: this.lastColumnWidth
	});

	this.lastColumnWidth = 0;
};

DocumentContext.prototype.beginColumn = function (width, offset, endingCell) {
	var saved = this.snapshots[this.snapshots.length - 1];

	this.calculateBottomMost(saved);

	this.endingCell = endingCell;
	this.page = saved.page;
	this.x = this.x + this.lastColumnWidth + (offset || 0);
	this.y = saved.y;
	this.availableWidth = width;	//saved.availableWidth - offset;
	this.availableHeight = saved.availableHeight;

	this.lastColumnWidth = width;
};

DocumentContext.prototype.calculateBottomMost = function (destContext) {
	if (this.endingCell) {
		this.saveContextInEndingCell(this.endingCell);
		this.endingCell = null;
	} else {
		destContext.bottomMost = bottomMostContext(this, destContext.bottomMost);
	}
};

DocumentContext.prototype.markEnding = function (endingCell) {
	this.page = endingCell._columnEndingContext.page;
	this.x = endingCell._columnEndingContext.x;
	this.y = endingCell._columnEndingContext.y;
	this.availableWidth = endingCell._columnEndingContext.availableWidth;
	this.availableHeight = endingCell._columnEndingContext.availableHeight;
	this.lastColumnWidth = endingCell._columnEndingContext.lastColumnWidth;
};

DocumentContext.prototype.saveContextInEndingCell = function (endingCell) {
	endingCell._columnEndingContext = {
		page: this.page,
		x: this.x,
		y: this.y,
		availableHeight: this.availableHeight,
		availableWidth: this.availableWidth,
		lastColumnWidth: this.lastColumnWidth
	};
};

DocumentContext.prototype.completeColumnGroup = function (height) {
	var saved = this.snapshots.pop();

	this.calculateBottomMost(saved);

	this.endingCell = null;
	this.x = saved.x;

	var y = saved.bottomMost.y;
	if (height) {
		if (saved.page === saved.bottomMost.page) {
			if ((saved.y + height) > y) {
				y = saved.y + height;
			}
		} else {
			y += height;
		}
	}

	this.y = y;
	this.page = saved.bottomMost.page;
	this.availableWidth = saved.availableWidth;
	this.availableHeight = saved.bottomMost.availableHeight;
	if (height) {
		this.availableHeight -= (y - saved.bottomMost.y);
	}
	this.lastColumnWidth = saved.lastColumnWidth;
};

DocumentContext.prototype.addMargin = function (left, right) {
	this.x += left;
	this.availableWidth -= left + (right || 0);
};

DocumentContext.prototype.moveDown = function (offset) {
	this.y += offset;
	this.availableHeight -= offset;

	return this.availableHeight > 0;
};

DocumentContext.prototype.initializePage = function () {
	this.y = this.pageMargins.top;
	this.availableHeight = this.getCurrentPage().pageSize.height - this.pageMargins.top - this.pageMargins.bottom;
	this.pageSnapshot().availableWidth = this.getCurrentPage().pageSize.width - this.pageMargins.left - this.pageMargins.right;
};

DocumentContext.prototype.pageSnapshot = function () {
	if (this.snapshots[0]) {
		return this.snapshots[0];
	} else {
		return this;
	}
};

DocumentContext.prototype.moveTo = function (x, y) {
	if (x !== undefined && x !== null) {
		this.x = x;
		this.availableWidth = this.getCurrentPage().pageSize.width - this.x - this.pageMargins.right;
	}
	if (y !== undefined && y !== null) {
		this.y = y;
		this.availableHeight = this.getCurrentPage().pageSize.height - this.y - this.pageMargins.bottom;
	}
};

DocumentContext.prototype.moveToRelative = function (x, y) {
	if (x !== undefined && x !== null) {
		this.x = this.x + x;
	}
	if (y !== undefined && y !== null) {
		this.y = this.y + y;
	}
};

DocumentContext.prototype.beginDetachedBlock = function () {
	this.snapshots.push({
		x: this.x,
		y: this.y,
		availableHeight: this.availableHeight,
		availableWidth: this.availableWidth,
		page: this.page,
		endingCell: this.endingCell,
		lastColumnWidth: this.lastColumnWidth
	});
};

DocumentContext.prototype.endDetachedBlock = function () {
	var saved = this.snapshots.pop();

	this.x = saved.x;
	this.y = saved.y;
	this.availableWidth = saved.availableWidth;
	this.availableHeight = saved.availableHeight;
	this.page = saved.page;
	this.endingCell = saved.endingCell;
	this.lastColumnWidth = saved.lastColumnWidth;
};

function pageOrientation(pageOrientationString, currentPageOrientation) {
	if (pageOrientationString === undefined) {
		return currentPageOrientation;
	} else if (isString(pageOrientationString) && (pageOrientationString.toLowerCase() === 'landscape')) {
		return 'landscape';
	} else {
		return 'portrait';
	}
}

var getPageSize = function (currentPage, newPageOrientation) {

	newPageOrientation = pageOrientation(newPageOrientation, currentPage.pageSize.orientation);

	if (newPageOrientation !== currentPage.pageSize.orientation) {
		return {
			orientation: newPageOrientation,
			width: currentPage.pageSize.height,
			height: currentPage.pageSize.width
		};
	} else {
		return {
			orientation: currentPage.pageSize.orientation,
			width: currentPage.pageSize.width,
			height: currentPage.pageSize.height
		};
	}

};


DocumentContext.prototype.moveToNextPage = function (pageOrientation) {
	var nextPageIndex = this.page + 1;

	var prevPage = this.page;
	var prevY = this.y;

	var createNewPage = nextPageIndex >= this.pages.length;
	if (createNewPage) {
		var currentAvailableWidth = this.availableWidth;
		var currentPageOrientation = this.getCurrentPage().pageSize.orientation;

		var pageSize = getPageSize(this.getCurrentPage(), pageOrientation);
		this.addPage(pageSize);

		if (currentPageOrientation === pageSize.orientation) {
			this.availableWidth = currentAvailableWidth;
		}
	} else {
		this.page = nextPageIndex;
		this.initializePage();
	}

	return {
		newPageCreated: createNewPage,
		prevPage: prevPage,
		prevY: prevY,
		y: this.y
	};
};


DocumentContext.prototype.addPage = function (pageSize) {
	var page = { items: [], pageSize: pageSize };
	this.pages.push(page);
	this.backgroundLength.push(0);
	this.page = this.pages.length - 1;
	this.initializePage();

	this.tracker.emit('pageAdded');

	return page;
};

DocumentContext.prototype.getCurrentPage = function () {
	if (this.page < 0 || this.page >= this.pages.length) {
		return null;
	}

	return this.pages[this.page];
};

DocumentContext.prototype.getCurrentPosition = function () {
	var pageSize = this.getCurrentPage().pageSize;
	var innerHeight = pageSize.height - this.pageMargins.top - this.pageMargins.bottom;
	var innerWidth = pageSize.width - this.pageMargins.left - this.pageMargins.right;

	return {
		pageNumber: this.page + 1,
		pageOrientation: pageSize.orientation,
		pageInnerHeight: innerHeight,
		pageInnerWidth: innerWidth,
		left: this.x,
		top: this.y,
		verticalRatio: ((this.y - this.pageMargins.top) / innerHeight),
		horizontalRatio: ((this.x - this.pageMargins.left) / innerWidth)
	};
};

function bottomMostContext(c1, c2) {
	var r;

	if (c1.page > c2.page) {
		r = c1;
	} else if (c2.page > c1.page) {
		r = c2;
	} else {
		r = (c1.y > c2.y) ? c1 : c2;
	}

	return {
		page: r.page,
		x: r.x,
		y: r.y,
		availableHeight: r.availableHeight,
		availableWidth: r.availableWidth
	};
}

module.exports = DocumentContext;


/***/ }),
/* 294 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


/**
 * Creates an instance of Line
 *
 * @constructor
 * @this {Line}
 * @param {Number} Maximum width this line can have
 */
function Line(maxWidth) {
	this.maxWidth = maxWidth;
	this.leadingCut = 0;
	this.trailingCut = 0;
	this.inlineWidths = 0;
	this.inlines = [];
}

Line.prototype.getAscenderHeight = function () {
	var y = 0;

	this.inlines.forEach(function (inline) {
		y = Math.max(y, inline.font.ascender / 1000 * inline.fontSize);
	});
	return y;
};

Line.prototype.hasEnoughSpaceForInline = function (inline, nextInlines) {
	nextInlines = nextInlines || [];

	if (this.inlines.length === 0) {
		return true;
	}
	if (this.newLineForced) {
		return false;
	}

	var inlineWidth = inline.width;
	var inlineTrailingCut = inline.trailingCut || 0;
	if (inline.noNewLine) {
		for (var i = 0, l = nextInlines.length; i < l; i++) {
			var nextInline = nextInlines[i];
			inlineWidth += nextInline.width;
			inlineTrailingCut += nextInline.trailingCut || 0;
			if (!nextInline.noNewLine) {
				break;
			}
		}
	}

	return (this.inlineWidths + inlineWidth - this.leadingCut - inlineTrailingCut) <= this.maxWidth;
};

Line.prototype.addInline = function (inline) {
	if (this.inlines.length === 0) {
		this.leadingCut = inline.leadingCut || 0;
	}
	this.trailingCut = inline.trailingCut || 0;

	inline.x = this.inlineWidths - this.leadingCut;

	this.inlines.push(inline);
	this.inlineWidths += inline.width;

	if (inline.lineEnd) {
		this.newLineForced = true;
	}
};

Line.prototype.getWidth = function () {
	return this.inlineWidths - this.leadingCut - this.trailingCut;
};

Line.prototype.getAvailableWidth = function () {
	return this.maxWidth - this.getWidth();
};

/**
 * Returns line height
 * @return {Number}
 */
Line.prototype.getHeight = function () {
	var max = 0;

	this.inlines.forEach(function (item) {
		max = Math.max(max, item.height || 0);
	});

	return max;
};

module.exports = Line;


/***/ }),
/* 295 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);

module.exports = global.Promise;


/***/ }),
/* 296 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var fails = __webpack_require__(4);
var classof = __webpack_require__(30);
var bind = __webpack_require__(96);
var html = __webpack_require__(203);
var createElement = __webpack_require__(138);
var IS_IOS = __webpack_require__(297);

var location = global.location;
var set = global.setImmediate;
var clear = global.clearImmediate;
var process = global.process;
var MessageChannel = global.MessageChannel;
var Dispatch = global.Dispatch;
var counter = 0;
var queue = {};
var ONREADYSTATECHANGE = 'onreadystatechange';
var defer, channel, port;

var run = function (id) {
  // eslint-disable-next-line no-prototype-builtins
  if (queue.hasOwnProperty(id)) {
    var fn = queue[id];
    delete queue[id];
    fn();
  }
};

var runner = function (id) {
  return function () {
    run(id);
  };
};

var listener = function (event) {
  run(event.data);
};

var post = function (id) {
  // old engines have not location.origin
  global.postMessage(id + '', location.protocol + '//' + location.host);
};

// Node.js 0.9+ & IE10+ has setImmediate, otherwise:
if (!set || !clear) {
  set = function setImmediate(fn) {
    var args = [];
    var i = 1;
    while (arguments.length > i) args.push(arguments[i++]);
    queue[++counter] = function () {
      // eslint-disable-next-line no-new-func
      (typeof fn == 'function' ? fn : Function(fn)).apply(undefined, args);
    };
    defer(counter);
    return counter;
  };
  clear = function clearImmediate(id) {
    delete queue[id];
  };
  // Node.js 0.8-
  if (classof(process) == 'process') {
    defer = function (id) {
      process.nextTick(runner(id));
    };
  // Sphere (JS game engine) Dispatch API
  } else if (Dispatch && Dispatch.now) {
    defer = function (id) {
      Dispatch.now(runner(id));
    };
  // Browsers with MessageChannel, includes WebWorkers
  // except iOS - https://github.com/zloirock/core-js/issues/624
  } else if (MessageChannel && !IS_IOS) {
    channel = new MessageChannel();
    port = channel.port2;
    channel.port1.onmessage = listener;
    defer = bind(port.postMessage, port, 1);
  // Browsers with postMessage, skip WebWorkers
  // IE8 has postMessage, but it's sync & typeof its postMessage is 'object'
  } else if (
    global.addEventListener &&
    typeof postMessage == 'function' &&
    !global.importScripts &&
    !fails(post) &&
    location.protocol !== 'file:'
  ) {
    defer = post;
    global.addEventListener('message', listener, false);
  // IE8-
  } else if (ONREADYSTATECHANGE in createElement('script')) {
    defer = function (id) {
      html.appendChild(createElement('script'))[ONREADYSTATECHANGE] = function () {
        html.removeChild(this);
        run(id);
      };
    };
  // Rest old browsers
  } else {
    defer = function (id) {
      setTimeout(runner(id), 0);
    };
  }
}

module.exports = {
  set: set,
  clear: clear
};


/***/ }),
/* 297 */
/***/ (function(module, exports, __webpack_require__) {

var userAgent = __webpack_require__(210);

module.exports = /(iphone|ipod|ipad).*applewebkit/i.test(userAgent);


/***/ }),
/* 298 */
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__(16);
var isObject = __webpack_require__(13);
var newPromiseCapability = __webpack_require__(109);

module.exports = function (C, x) {
  anObject(C);
  if (isObject(x) && x.constructor === C) return x;
  var promiseCapability = newPromiseCapability.f(C);
  var resolve = promiseCapability.resolve;
  resolve(x);
  return promiseCapability.promise;
};


/***/ }),
/* 299 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var aFunction = __webpack_require__(44);
var newPromiseCapabilityModule = __webpack_require__(109);
var perform = __webpack_require__(137);
var iterate = __webpack_require__(136);

// `Promise.allSettled` method
// https://github.com/tc39/proposal-promise-allSettled
$({ target: 'Promise', stat: true }, {
  allSettled: function allSettled(iterable) {
    var C = this;
    var capability = newPromiseCapabilityModule.f(C);
    var resolve = capability.resolve;
    var reject = capability.reject;
    var result = perform(function () {
      var promiseResolve = aFunction(C.resolve);
      var values = [];
      var counter = 0;
      var remaining = 1;
      iterate(iterable, function (promise) {
        var index = counter++;
        var alreadyCalled = false;
        values.push(undefined);
        remaining++;
        promiseResolve.call(C, promise).then(function (value) {
          if (alreadyCalled) return;
          alreadyCalled = true;
          values[index] = { status: 'fulfilled', value: value };
          --remaining || resolve(values);
        }, function (e) {
          if (alreadyCalled) return;
          alreadyCalled = true;
          values[index] = { status: 'rejected', reason: e };
          --remaining || resolve(values);
        });
      });
      --remaining || resolve(values);
    });
    if (result.error) reject(result.value);
    return capability.promise;
  }
});


/***/ }),
/* 300 */
/***/ (function(module, exports, __webpack_require__) {

var ___EXPOSE_LOADER_IMPORT___ = __webpack_require__(301);
var ___EXPOSE_LOADER_GET_GLOBAL_THIS___ = __webpack_require__(563);
var ___EXPOSE_LOADER_GLOBAL_THIS___ = ___EXPOSE_LOADER_GET_GLOBAL_THIS___;
if (typeof ___EXPOSE_LOADER_GLOBAL_THIS___["pdfMake"] === 'undefined') ___EXPOSE_LOADER_GLOBAL_THIS___["pdfMake"] = ___EXPOSE_LOADER_IMPORT___;
module.exports = ___EXPOSE_LOADER_IMPORT___;


/***/ }),
/* 301 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(Buffer, global) {

var isFunction = __webpack_require__(0).isFunction;
var isUndefined = __webpack_require__(0).isUndefined;
var isNull = __webpack_require__(0).isNull;
var FileSaver = __webpack_require__(304);
var saveAs = FileSaver.saveAs;

var defaultClientFonts = {
	Roboto: {
		normal: 'Roboto-Regular.ttf',
		bold: 'Roboto-Medium.ttf',
		italics: 'Roboto-Italic.ttf',
		bolditalics: 'Roboto-MediumItalic.ttf'
	}
};

function Document(docDefinition, tableLayouts, fonts, vfs) {
	this.docDefinition = docDefinition;
	this.tableLayouts = tableLayouts || null;
	this.fonts = fonts || defaultClientFonts;
	this.vfs = vfs;
}

function canCreatePdf() {
	// Ensure the browser provides the level of support needed
	if (!Object.keys || typeof Uint16Array === 'undefined') {
		return false;
	}
	return true;
}

Document.prototype._createDoc = function (options, cb) {
	options = options || {};
	if (this.tableLayouts) {
		options.tableLayouts = this.tableLayouts;
	}

	var PdfPrinter = __webpack_require__(305);

	var printer = new PdfPrinter(this.fonts);
	__webpack_require__(53).bindFS(this.vfs); // bind virtual file system to file system

	if (!isFunction(cb)) {
		var doc = printer.createPdfKitDocument(this.docDefinition, options);

		return doc;
	}

	var URLBrowserResolver = __webpack_require__(549);
	var urlResolver = new URLBrowserResolver(__webpack_require__(53));

	for (var font in this.fonts) {
		if (this.fonts.hasOwnProperty(font)) {
			if (this.fonts[font].normal) {
				urlResolver.resolve(this.fonts[font].normal);
			}
			if (this.fonts[font].bold) {
				urlResolver.resolve(this.fonts[font].bold);
			}
			if (this.fonts[font].italics) {
				urlResolver.resolve(this.fonts[font].italics);
			}
			if (this.fonts[font].bolditalics) {
				urlResolver.resolve(this.fonts[font].bolditalics);
			}
		}
	}

	if (this.docDefinition.images) {
		for (var image in this.docDefinition.images) {
			if (this.docDefinition.images.hasOwnProperty(image)) {
				urlResolver.resolve(this.docDefinition.images[image]);
			}
		}
	}

	var _this = this;

	urlResolver.resolved().then(function () {
		var doc = printer.createPdfKitDocument(_this.docDefinition, options);

		cb(doc);
	}, function (result) {
		throw result;
	});
};

Document.prototype._flushDoc = function (doc, callback) {
	var chunks = [];
	var result;

	doc.on('readable', function () {
		var chunk;
		while ((chunk = doc.read(9007199254740991)) !== null) {
			chunks.push(chunk);
		}
	});
	doc.on('end', function () {
		result = Buffer.concat(chunks);
		callback(result, doc._pdfMakePages);
	});
	doc.end();
};

Document.prototype._getPages = function (options, cb) {
	if (!cb) {
		throw '_getPages is an async method and needs a callback argument';
	}
	var _this = this;

	this._createDoc(options, function (doc) {
		_this._flushDoc(doc, function (ignoreBuffer, pages) {
			cb(pages);
		});
	});
};

Document.prototype._bufferToBlob = function (buffer) {
	var blob;
	try {
		blob = new Blob([buffer], { type: 'application/pdf' });
	} catch (e) {
		// Old browser which can't handle it without making it an byte array (ie10)
		if (e.name === 'InvalidStateError') {
			var byteArray = new Uint8Array(buffer);
			blob = new Blob([byteArray.buffer], { type: 'application/pdf' });
		}
	}

	if (!blob) {
		throw 'Could not generate blob';
	}

	return blob;
};

Document.prototype._openWindow = function () {
	// we have to open the window immediately and store the reference
	// otherwise popup blockers will stop us
	var win = window.open('', '_blank');
	if (win === null) {
		throw 'Open PDF in new window blocked by browser';
	}

	return win;
};

Document.prototype._openPdf = function (options, win) {
	if (!win) {
		win = this._openWindow();
	}
	try {
		this.getBlob(function (result) {
			var urlCreator = window.URL || window.webkitURL;
			var pdfUrl = urlCreator.createObjectURL(result);
			win.location.href = pdfUrl;

			/* temporarily disabled
			if (win !== window) {
				setTimeout(function () {
					if (isNull(win.window)) { // is closed by AdBlock
						window.location.href = pdfUrl; // open in actual window
					}
				}, 500);
			}
			*/
		}, options);
	} catch (e) {
		win.close();
		throw e;
	}
};

Document.prototype.open = function (options, win) {
	options = options || {};
	options.autoPrint = false;
	win = win || null;

	this._openPdf(options, win);
};


Document.prototype.print = function (options, win) {
	options = options || {};
	options.autoPrint = true;
	win = win || null;

	this._openPdf(options, win);
};

/**
 * download(defaultFileName = 'file.pdf', cb = null, options = {})
 * or
 * download(cb, options = {})
 */
Document.prototype.download = function (defaultFileName, cb, options) {
	if (isFunction(defaultFileName)) {
		if (!isUndefined(cb)) {
			options = cb;
		}
		cb = defaultFileName;
		defaultFileName = null;
	}

	defaultFileName = defaultFileName || 'file.pdf';
	this.getBlob(function (result) {
		saveAs(result, defaultFileName);

		if (isFunction(cb)) {
			cb();
		}
	}, options);
};

Document.prototype.getBase64 = function (cb, options) {
	if (!cb) {
		throw 'getBase64 is an async method and needs a callback argument';
	}
	this.getBuffer(function (buffer) {
		cb(buffer.toString('base64'));
	}, options);
};

Document.prototype.getDataUrl = function (cb, options) {
	if (!cb) {
		throw 'getDataUrl is an async method and needs a callback argument';
	}
	this.getBuffer(function (buffer) {
		cb('data:application/pdf;base64,' + buffer.toString('base64'));
	}, options);
};

Document.prototype.getBlob = function (cb, options) {
	if (!cb) {
		throw 'getBlob is an async method and needs a callback argument';
	}
	var that = this;
	this.getBuffer(function (result) {
		var blob = that._bufferToBlob(result);
		cb(blob);
	}, options);
};

Document.prototype.getBuffer = function (cb, options) {
	if (!cb) {
		throw 'getBuffer is an async method and needs a callback argument';
	}

	var _this = this;

	this._createDoc(options, function (doc) {
		_this._flushDoc(doc, function (buffer) {
			cb(buffer);
		});
	});
};

Document.prototype.getStream = function (options, cb) {
	if (!isFunction(cb)) {
		var doc = this._createDoc(options);
		return doc;
	}

	this._createDoc(options, function (doc) {
		cb(doc);
	});
};

module.exports = {
	createPdf: function (docDefinition, tableLayouts, fonts, vfs) {
		if (!canCreatePdf()) {
			throw 'Your browser does not provide the level of support needed';
		}
		return new Document(
			docDefinition,
			tableLayouts || global.pdfMake.tableLayouts,
			fonts || global.pdfMake.fonts,
			vfs || global.pdfMake.vfs
		);
	}
};

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer, __webpack_require__(24)))

/***/ }),
/* 302 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


exports.byteLength = byteLength
exports.toByteArray = toByteArray
exports.fromByteArray = fromByteArray

var lookup = []
var revLookup = []
var Arr = typeof Uint8Array !== 'undefined' ? Uint8Array : Array

var code = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
for (var i = 0, len = code.length; i < len; ++i) {
  lookup[i] = code[i]
  revLookup[code.charCodeAt(i)] = i
}

// Support decoding URL-safe base64 strings, as Node.js does.
// See: https://en.wikipedia.org/wiki/Base64#URL_applications
revLookup['-'.charCodeAt(0)] = 62
revLookup['_'.charCodeAt(0)] = 63

function getLens (b64) {
  var len = b64.length

  if (len % 4 > 0) {
    throw new Error('Invalid string. Length must be a multiple of 4')
  }

  // Trim off extra bytes after placeholder bytes are found
  // See: https://github.com/beatgammit/base64-js/issues/42
  var validLen = b64.indexOf('=')
  if (validLen === -1) validLen = len

  var placeHoldersLen = validLen === len
    ? 0
    : 4 - (validLen % 4)

  return [validLen, placeHoldersLen]
}

// base64 is 4/3 + up to two characters of the original data
function byteLength (b64) {
  var lens = getLens(b64)
  var validLen = lens[0]
  var placeHoldersLen = lens[1]
  return ((validLen + placeHoldersLen) * 3 / 4) - placeHoldersLen
}

function _byteLength (b64, validLen, placeHoldersLen) {
  return ((validLen + placeHoldersLen) * 3 / 4) - placeHoldersLen
}

function toByteArray (b64) {
  var tmp
  var lens = getLens(b64)
  var validLen = lens[0]
  var placeHoldersLen = lens[1]

  var arr = new Arr(_byteLength(b64, validLen, placeHoldersLen))

  var curByte = 0

  // if there are placeholders, only get up to the last complete 4 chars
  var len = placeHoldersLen > 0
    ? validLen - 4
    : validLen

  var i
  for (i = 0; i < len; i += 4) {
    tmp =
      (revLookup[b64.charCodeAt(i)] << 18) |
      (revLookup[b64.charCodeAt(i + 1)] << 12) |
      (revLookup[b64.charCodeAt(i + 2)] << 6) |
      revLookup[b64.charCodeAt(i + 3)]
    arr[curByte++] = (tmp >> 16) & 0xFF
    arr[curByte++] = (tmp >> 8) & 0xFF
    arr[curByte++] = tmp & 0xFF
  }

  if (placeHoldersLen === 2) {
    tmp =
      (revLookup[b64.charCodeAt(i)] << 2) |
      (revLookup[b64.charCodeAt(i + 1)] >> 4)
    arr[curByte++] = tmp & 0xFF
  }

  if (placeHoldersLen === 1) {
    tmp =
      (revLookup[b64.charCodeAt(i)] << 10) |
      (revLookup[b64.charCodeAt(i + 1)] << 4) |
      (revLookup[b64.charCodeAt(i + 2)] >> 2)
    arr[curByte++] = (tmp >> 8) & 0xFF
    arr[curByte++] = tmp & 0xFF
  }

  return arr
}

function tripletToBase64 (num) {
  return lookup[num >> 18 & 0x3F] +
    lookup[num >> 12 & 0x3F] +
    lookup[num >> 6 & 0x3F] +
    lookup[num & 0x3F]
}

function encodeChunk (uint8, start, end) {
  var tmp
  var output = []
  for (var i = start; i < end; i += 3) {
    tmp =
      ((uint8[i] << 16) & 0xFF0000) +
      ((uint8[i + 1] << 8) & 0xFF00) +
      (uint8[i + 2] & 0xFF)
    output.push(tripletToBase64(tmp))
  }
  return output.join('')
}

function fromByteArray (uint8) {
  var tmp
  var len = uint8.length
  var extraBytes = len % 3 // if we have 1 byte left, pad 2 bytes
  var parts = []
  var maxChunkLength = 16383 // must be multiple of 3

  // go through the array every three bytes, we'll deal with trailing stuff later
  for (var i = 0, len2 = len - extraBytes; i < len2; i += maxChunkLength) {
    parts.push(encodeChunk(
      uint8, i, (i + maxChunkLength) > len2 ? len2 : (i + maxChunkLength)
    ))
  }

  // pad the end with zeros, but make sure to not forget the extra bytes
  if (extraBytes === 1) {
    tmp = uint8[len - 1]
    parts.push(
      lookup[tmp >> 2] +
      lookup[(tmp << 4) & 0x3F] +
      '=='
    )
  } else if (extraBytes === 2) {
    tmp = (uint8[len - 2] << 8) + uint8[len - 1]
    parts.push(
      lookup[tmp >> 10] +
      lookup[(tmp >> 4) & 0x3F] +
      lookup[(tmp << 2) & 0x3F] +
      '='
    )
  }

  return parts.join('')
}


/***/ }),
/* 303 */
/***/ (function(module, exports) {

exports.read = function (buffer, offset, isLE, mLen, nBytes) {
  var e, m
  var eLen = (nBytes * 8) - mLen - 1
  var eMax = (1 << eLen) - 1
  var eBias = eMax >> 1
  var nBits = -7
  var i = isLE ? (nBytes - 1) : 0
  var d = isLE ? -1 : 1
  var s = buffer[offset + i]

  i += d

  e = s & ((1 << (-nBits)) - 1)
  s >>= (-nBits)
  nBits += eLen
  for (; nBits > 0; e = (e * 256) + buffer[offset + i], i += d, nBits -= 8) {}

  m = e & ((1 << (-nBits)) - 1)
  e >>= (-nBits)
  nBits += mLen
  for (; nBits > 0; m = (m * 256) + buffer[offset + i], i += d, nBits -= 8) {}

  if (e === 0) {
    e = 1 - eBias
  } else if (e === eMax) {
    return m ? NaN : ((s ? -1 : 1) * Infinity)
  } else {
    m = m + Math.pow(2, mLen)
    e = e - eBias
  }
  return (s ? -1 : 1) * m * Math.pow(2, e - mLen)
}

exports.write = function (buffer, value, offset, isLE, mLen, nBytes) {
  var e, m, c
  var eLen = (nBytes * 8) - mLen - 1
  var eMax = (1 << eLen) - 1
  var eBias = eMax >> 1
  var rt = (mLen === 23 ? Math.pow(2, -24) - Math.pow(2, -77) : 0)
  var i = isLE ? 0 : (nBytes - 1)
  var d = isLE ? 1 : -1
  var s = value < 0 || (value === 0 && 1 / value < 0) ? 1 : 0

  value = Math.abs(value)

  if (isNaN(value) || value === Infinity) {
    m = isNaN(value) ? 1 : 0
    e = eMax
  } else {
    e = Math.floor(Math.log(value) / Math.LN2)
    if (value * (c = Math.pow(2, -e)) < 1) {
      e--
      c *= 2
    }
    if (e + eBias >= 1) {
      value += rt / c
    } else {
      value += rt * Math.pow(2, 1 - eBias)
    }
    if (value * c >= 2) {
      e++
      c /= 2
    }

    if (e + eBias >= eMax) {
      m = 0
      e = eMax
    } else if (e + eBias >= 1) {
      m = ((value * c) - 1) * Math.pow(2, mLen)
      e = e + eBias
    } else {
      m = value * Math.pow(2, eBias - 1) * Math.pow(2, mLen)
      e = 0
    }
  }

  for (; mLen >= 8; buffer[offset + i] = m & 0xff, i += d, m /= 256, mLen -= 8) {}

  e = (e << mLen) | m
  eLen += mLen
  for (; eLen > 0; buffer[offset + i] = e & 0xff, i += d, e /= 256, eLen -= 8) {}

  buffer[offset + i - d] |= s * 128
}


/***/ }),
/* 304 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(global) {var __WEBPACK_AMD_DEFINE_FACTORY__, __WEBPACK_AMD_DEFINE_ARRAY__, __WEBPACK_AMD_DEFINE_RESULT__;(function(a,b){if(true)!(__WEBPACK_AMD_DEFINE_ARRAY__ = [], __WEBPACK_AMD_DEFINE_FACTORY__ = (b),
				__WEBPACK_AMD_DEFINE_RESULT__ = (typeof __WEBPACK_AMD_DEFINE_FACTORY__ === 'function' ?
				(__WEBPACK_AMD_DEFINE_FACTORY__.apply(exports, __WEBPACK_AMD_DEFINE_ARRAY__)) : __WEBPACK_AMD_DEFINE_FACTORY__),
				__WEBPACK_AMD_DEFINE_RESULT__ !== undefined && (module.exports = __WEBPACK_AMD_DEFINE_RESULT__));else {}})(this,function(){"use strict";function b(a,b){return"undefined"==typeof b?b={autoBom:!1}:"object"!=typeof b&&(console.warn("Deprecated: Expected third argument to be a object"),b={autoBom:!b}),b.autoBom&&/^\s*(?:text\/\S*|application\/xml|\S*\/\S*\+xml)\s*;.*charset\s*=\s*utf-8/i.test(a.type)?new Blob(["\uFEFF",a],{type:a.type}):a}function c(b,c,d){var e=new XMLHttpRequest;e.open("GET",b),e.responseType="blob",e.onload=function(){a(e.response,c,d)},e.onerror=function(){console.error("could not download file")},e.send()}function d(a){var b=new XMLHttpRequest;b.open("HEAD",a,!1);try{b.send()}catch(a){}return 200<=b.status&&299>=b.status}function e(a){try{a.dispatchEvent(new MouseEvent("click"))}catch(c){var b=document.createEvent("MouseEvents");b.initMouseEvent("click",!0,!0,window,0,0,0,80,20,!1,!1,!1,!1,0,null),a.dispatchEvent(b)}}var f="object"==typeof window&&window.window===window?window:"object"==typeof self&&self.self===self?self:"object"==typeof global&&global.global===global?global:void 0,a=f.saveAs||("object"!=typeof window||window!==f?function(){}:(typeof HTMLAnchorElement !== "undefined" && "download" in HTMLAnchorElement.prototype)?function(b,g,h){var i=f.URL||f.webkitURL,j=document.createElement("a");g=g||b.name||"download",j.download=g,j.rel="noopener","string"==typeof b?(j.href=b,j.origin===location.origin?e(j):d(j.href)?c(b,g,h):e(j,j.target="_blank")):(j.href=i.createObjectURL(b),setTimeout(function(){i.revokeObjectURL(j.href)},4E4),setTimeout(function(){e(j)},0))}:"msSaveOrOpenBlob"in navigator?function(f,g,h){if(g=g||f.name||"download","string"!=typeof f)navigator.msSaveOrOpenBlob(b(f,h),g);else if(d(f))c(f,g,h);else{var i=document.createElement("a");i.href=f,i.target="_blank",setTimeout(function(){e(i)})}}:function(a,b,d,e){if(e=e||open("","_blank"),e&&(e.document.title=e.document.body.innerText="downloading..."),"string"==typeof a)return c(a,b,d);var g="application/octet-stream"===a.type,h=/constructor/i.test(f.HTMLElement)||f.safari,i=/CriOS\/[\d]+/.test(navigator.userAgent);if((i||g&&h)&&"object"==typeof FileReader){var j=new FileReader;j.onloadend=function(){var a=j.result;a=i?a:a.replace(/^data:[^;]*;/,"data:attachment/file;"),e?e.location.href=a:location=a,e=null},j.readAsDataURL(a)}else{var k=f.URL||f.webkitURL,l=k.createObjectURL(a);e?e.location=l:location.href=l,e=null,setTimeout(function(){k.revokeObjectURL(l)},4E4)}});f.saveAs=a.saveAs=a, true&&(module.exports=a)});

//# sourceMappingURL=FileSaver.min.js.map
/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(24)))

/***/ }),
/* 305 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/*eslint no-unused-vars: ["error", {"args": "none"}]*/


var PdfKitEngine = __webpack_require__(306);
var FontProvider = __webpack_require__(532);
var LayoutBuilder = __webpack_require__(533);
var sizes = __webpack_require__(540);
var ImageMeasure = __webpack_require__(541);
var SVGMeasure = __webpack_require__(542);
var textDecorator = __webpack_require__(546);
var TextTools = __webpack_require__(194);
var isFunction = __webpack_require__(0).isFunction;
var isString = __webpack_require__(0).isString;
var isNumber = __webpack_require__(0).isNumber;
var isBoolean = __webpack_require__(0).isBoolean;
var isArray = __webpack_require__(0).isArray;
var isUndefined = __webpack_require__(0).isUndefined;

var getSvgToPDF = function () {
	try {
		// optional dependency to support svg nodes
		return __webpack_require__(547);
	} catch (e) {
		throw new Error('Please install svg-to-pdfkit to enable svg nodes');
	}
};

var findFont = function (fonts, requiredFonts, defaultFont) {
	for (var i = 0; i < requiredFonts.length; i++) {
		var requiredFont = requiredFonts[i].toLowerCase();

		for (var font in fonts) {
			if (font.toLowerCase() === requiredFont) {
				return font;
			}
		}
	}

	return defaultFont;
};

////////////////////////////////////////
// PdfPrinter

/**
 * @class Creates an instance of a PdfPrinter which turns document definition into a pdf
 *
 * @param {Object} fontDescriptors font definition dictionary
 *
 * @example
 * var fontDescriptors = {
 *	Roboto: {
 *		normal: 'fonts/Roboto-Regular.ttf',
 *		bold: 'fonts/Roboto-Medium.ttf',
 *		italics: 'fonts/Roboto-Italic.ttf',
 *		bolditalics: 'fonts/Roboto-MediumItalic.ttf'
 *	}
 * };
 *
 * var printer = new PdfPrinter(fontDescriptors);
 */
function PdfPrinter(fontDescriptors) {
	this.fontDescriptors = fontDescriptors;
}

/**
 * Executes layout engine for the specified document and renders it into a pdfkit document
 * ready to be saved.
 *
 * @param {Object} docDefinition document definition
 * @param {Object} docDefinition.content an array describing the pdf structure (for more information take a look at the examples in the /examples folder)
 * @param {Object} [docDefinition.defaultStyle] default (implicit) style definition
 * @param {Object} [docDefinition.styles] dictionary defining all styles which can be used in the document
 * @param {Object} [docDefinition.pageSize] page size (pdfkit units, A4 dimensions by default)
 * @param {Number} docDefinition.pageSize.width width
 * @param {Number} docDefinition.pageSize.height height
 * @param {Object} [docDefinition.pageMargins] page margins (pdfkit units)
 * @param {Number} docDefinition.maxPagesNumber maximum number of pages to render
 *
 * @example
 *
 * var docDefinition = {
 * 	info: {
 *		title: 'awesome Document',
 *		author: 'john doe',
 *		subject: 'subject of document',
 *		keywords: 'keywords for document',
 * 	},
 *	content: [
 *		'First paragraph',
 *		'Second paragraph, this time a little bit longer',
 *		{ text: 'Third paragraph, slightly bigger font size', fontSize: 20 },
 *		{ text: 'Another paragraph using a named style', style: 'header' },
 *		{ text: ['playing with ', 'inlines' ] },
 *		{ text: ['and ', { text: 'restyling ', bold: true }, 'them'] },
 *	],
 *	styles: {
 *		header: { fontSize: 30, bold: true }
 *	}
 * }
 *
 * var pdfKitDoc = printer.createPdfKitDocument(docDefinition);
 *
 * pdfKitDoc.pipe(fs.createWriteStream('sample.pdf'));
 * pdfKitDoc.end();
 *
 * @return {Object} a pdfKit document object which can be saved or encode to data-url
 */
PdfPrinter.prototype.createPdfKitDocument = function (docDefinition, options) {
	options = options || {};

	docDefinition.version = docDefinition.version || '1.3';
	docDefinition.compress = isBoolean(docDefinition.compress) ? docDefinition.compress : true;
	docDefinition.images = docDefinition.images || {};
	docDefinition.pageMargins = ((docDefinition.pageMargins !== undefined) && (docDefinition.pageMargins !== null)) ? docDefinition.pageMargins : 40;

	var pageSize = fixPageSize(docDefinition.pageSize, docDefinition.pageOrientation);

	var pdfOptions = {
		size: [pageSize.width, pageSize.height],
		pdfVersion: docDefinition.version,
		compress: docDefinition.compress,
		userPassword: docDefinition.userPassword,
		ownerPassword: docDefinition.ownerPassword,
		permissions: docDefinition.permissions,
		fontLayoutCache: isBoolean(options.fontLayoutCache) ? options.fontLayoutCache : true,
		bufferPages: options.bufferPages || false,
		autoFirstPage: false,
		font: null
	};

	this.pdfKitDoc = PdfKitEngine.createPdfDocument(pdfOptions);
	setMetadata(docDefinition, this.pdfKitDoc);

	this.fontProvider = new FontProvider(this.fontDescriptors, this.pdfKitDoc);

	var builder = new LayoutBuilder(pageSize, fixPageMargins(docDefinition.pageMargins), new ImageMeasure(this.pdfKitDoc, docDefinition.images), new SVGMeasure());

	registerDefaultTableLayouts(builder);
	if (options.tableLayouts) {
		builder.registerTableLayouts(options.tableLayouts);
	}

	var pages = builder.layoutDocument(docDefinition.content, this.fontProvider, docDefinition.styles || {}, docDefinition.defaultStyle || {
		fontSize: 12,
		font: 'Roboto'
	}, docDefinition.background, docDefinition.header, docDefinition.footer, docDefinition.images, docDefinition.watermark, docDefinition.pageBreakBefore);
	var maxNumberPages = docDefinition.maxPagesNumber || -1;
	if (isNumber(maxNumberPages) && maxNumberPages > -1) {
		pages = pages.slice(0, maxNumberPages);
	}

	// if pageSize.height is set to Infinity, calculate the actual height of the page that
	// was laid out using the height of each of the items in the page.
	if (pageSize.height === Infinity) {
		var pageHeight = calculatePageHeight(pages, docDefinition.pageMargins);
		this.pdfKitDoc.options.size = [pageSize.width, pageHeight];
	}

	renderPages(pages, this.fontProvider, this.pdfKitDoc, options.progressCallback);

	if (options.autoPrint) {
		var printActionRef = this.pdfKitDoc.ref({
			Type: 'Action',
			S: 'Named',
			N: 'Print'
		});
		this.pdfKitDoc._root.data.OpenAction = printActionRef;
		printActionRef.end();
	}
	return this.pdfKitDoc;
};

function setMetadata(docDefinition, pdfKitDoc) {
	// PDF standard has these properties reserved: Title, Author, Subject, Keywords,
	// Creator, Producer, CreationDate, ModDate, Trapped.
	// To keep the pdfmake api consistent, the info field are defined lowercase.
	// Custom properties don't contain a space.
	function standardizePropertyKey(key) {
		var standardProperties = ['Title', 'Author', 'Subject', 'Keywords',
			'Creator', 'Producer', 'CreationDate', 'ModDate', 'Trapped'];
		var standardizedKey = key.charAt(0).toUpperCase() + key.slice(1);
		if (standardProperties.indexOf(standardizedKey) !== -1) {
			return standardizedKey;
		}

		return key.replace(/\s+/g, '');
	}

	pdfKitDoc.info.Producer = 'pdfmake';
	pdfKitDoc.info.Creator = 'pdfmake';

	if (docDefinition.info) {
		for (var key in docDefinition.info) {
			var value = docDefinition.info[key];
			if (value) {
				key = standardizePropertyKey(key);
				pdfKitDoc.info[key] = value;
			}
		}
	}
}

function calculatePageHeight(pages, margins) {
	function getItemHeight(item) {
		if (isFunction(item.item.getHeight)) {
			return item.item.getHeight();
		} else if (item.item._height) {
			return item.item._height;
		} else {
			// TODO: add support for next item types
			return 0;
		}
	}

	function getBottomPosition(item) {
		var top = item.item.y;
		var height = getItemHeight(item);
		return top + height;
	}

	var fixedMargins = fixPageMargins(margins || 40);
	var height = fixedMargins.top;

	pages.forEach(function (page) {
		page.items.forEach(function (item) {
			var bottomPosition = getBottomPosition(item);
			if (bottomPosition > height) {
				height = bottomPosition;
			}
		});
	});

	height += fixedMargins.bottom;

	return height;
}

function fixPageSize(pageSize, pageOrientation) {
	function isNeedSwapPageSizes(pageOrientation) {
		if (isString(pageOrientation)) {
			pageOrientation = pageOrientation.toLowerCase();
			return ((pageOrientation === 'portrait') && (size.width > size.height)) ||
				((pageOrientation === 'landscape') && (size.width < size.height));
		}
		return false;
	}

	// if pageSize.height is set to auto, set the height to infinity so there are no page breaks.
	if (pageSize && pageSize.height === 'auto') {
		pageSize.height = Infinity;
	}

	var size = pageSize2widthAndHeight(pageSize || 'A4');
	if (isNeedSwapPageSizes(pageOrientation)) { // swap page sizes
		size = { width: size.height, height: size.width };
	}
	size.orientation = size.width > size.height ? 'landscape' : 'portrait';
	return size;
}

function fixPageMargins(margin) {
	if (isNumber(margin)) {
		margin = { left: margin, right: margin, top: margin, bottom: margin };
	} else if (isArray(margin)) {
		if (margin.length === 2) {
			margin = { left: margin[0], top: margin[1], right: margin[0], bottom: margin[1] };
		} else if (margin.length === 4) {
			margin = { left: margin[0], top: margin[1], right: margin[2], bottom: margin[3] };
		} else {
			throw 'Invalid pageMargins definition';
		}
	}

	return margin;
}

function registerDefaultTableLayouts(layoutBuilder) {
	layoutBuilder.registerTableLayouts({
		noBorders: {
			hLineWidth: function (i) {
				return 0;
			},
			vLineWidth: function (i) {
				return 0;
			},
			paddingLeft: function (i) {
				return i && 4 || 0;
			},
			paddingRight: function (i, node) {
				return (i < node.table.widths.length - 1) ? 4 : 0;
			}
		},
		headerLineOnly: {
			hLineWidth: function (i, node) {
				if (i === 0 || i === node.table.body.length) {
					return 0;
				}
				return (i === node.table.headerRows) ? 2 : 0;
			},
			vLineWidth: function (i) {
				return 0;
			},
			paddingLeft: function (i) {
				return i === 0 ? 0 : 8;
			},
			paddingRight: function (i, node) {
				return (i === node.table.widths.length - 1) ? 0 : 8;
			}
		},
		lightHorizontalLines: {
			hLineWidth: function (i, node) {
				if (i === 0 || i === node.table.body.length) {
					return 0;
				}
				return (i === node.table.headerRows) ? 2 : 1;
			},
			vLineWidth: function (i) {
				return 0;
			},
			hLineColor: function (i) {
				return i === 1 ? 'black' : '#aaa';
			},
			paddingLeft: function (i) {
				return i === 0 ? 0 : 8;
			},
			paddingRight: function (i, node) {
				return (i === node.table.widths.length - 1) ? 0 : 8;
			}
		}
	});
}

function pageSize2widthAndHeight(pageSize) {
	if (isString(pageSize)) {
		var size = sizes[pageSize.toUpperCase()];
		if (!size) {
			throw 'Page size ' + pageSize + ' not recognized';
		}
		return { width: size[0], height: size[1] };
	}

	return pageSize;
}

function updatePageOrientationInOptions(currentPage, pdfKitDoc) {
	var previousPageOrientation = pdfKitDoc.options.size[0] > pdfKitDoc.options.size[1] ? 'landscape' : 'portrait';

	if (currentPage.pageSize.orientation !== previousPageOrientation) {
		var width = pdfKitDoc.options.size[0];
		var height = pdfKitDoc.options.size[1];
		pdfKitDoc.options.size = [height, width];
	}
}

function renderPages(pages, fontProvider, pdfKitDoc, progressCallback) {
	pdfKitDoc._pdfMakePages = pages;
	pdfKitDoc.addPage();

	var totalItems = 0;
	if (progressCallback) {
		pages.forEach(function (page) {
			totalItems += page.items.length;
		});
	}

	var renderedItems = 0;
	progressCallback = progressCallback || function () {
	};

	for (var i = 0; i < pages.length; i++) {
		if (i > 0) {
			updatePageOrientationInOptions(pages[i], pdfKitDoc);
			pdfKitDoc.addPage(pdfKitDoc.options);
		}

		var page = pages[i];
		for (var ii = 0, il = page.items.length; ii < il; ii++) {
			var item = page.items[ii];
			switch (item.type) {
				case 'vector':
					renderVector(item.item, pdfKitDoc);
					break;
				case 'line':
					renderLine(item.item, item.item.x, item.item.y, pdfKitDoc);
					break;
				case 'image':
					renderImage(item.item, item.item.x, item.item.y, pdfKitDoc);
					break;
				case 'svg':
					renderSVG(item.item, item.item.x, item.item.y, pdfKitDoc, fontProvider);
					break;
				case 'beginClip':
					beginClip(item.item, pdfKitDoc);
					break;
				case 'endClip':
					endClip(pdfKitDoc);
					break;
			}
			renderedItems++;
			progressCallback(renderedItems / totalItems);
		}
		if (page.watermark) {
			renderWatermark(page, pdfKitDoc);
		}
	}
}

function renderLine(line, x, y, pdfKitDoc) {
	function preparePageNodeRefLine(_pageNodeRef, inline) {
		var newWidth;
		var diffWidth;
		var textTools = new TextTools(null);

		if (isUndefined(_pageNodeRef.positions)) {
			throw 'Page reference id not found';
		}

		var pageNumber = _pageNodeRef.positions[0].pageNumber.toString();

		inline.text = pageNumber;
		newWidth = textTools.widthOfString(inline.text, inline.font, inline.fontSize, inline.characterSpacing, inline.fontFeatures);
		diffWidth = inline.width - newWidth;
		inline.width = newWidth;

		switch (inline.alignment) {
			case 'right':
				inline.x += diffWidth;
				break;
			case 'center':
				inline.x += diffWidth / 2;
				break;
		}
	}

	if (line._pageNodeRef) {
		preparePageNodeRefLine(line._pageNodeRef, line.inlines[0]);
	}

	x = x || 0;
	y = y || 0;

	var lineHeight = line.getHeight();
	var ascenderHeight = line.getAscenderHeight();
	var descent = lineHeight - ascenderHeight;

	textDecorator.drawBackground(line, x, y, pdfKitDoc);

	//TODO: line.optimizeInlines();
	for (var i = 0, l = line.inlines.length; i < l; i++) {
		var inline = line.inlines[i];
		var shiftToBaseline = lineHeight - ((inline.font.ascender / 1000) * inline.fontSize) - descent;

		if (inline._pageNodeRef) {
			preparePageNodeRefLine(inline._pageNodeRef, inline);
		}

		var options = {
			lineBreak: false,
			textWidth: inline.width,
			characterSpacing: inline.characterSpacing,
			wordCount: 1,
			link: inline.link
		};

		if (inline.linkToDestination) {
			options.goTo = inline.linkToDestination;
		}

		if (line.id && i === 0) {
			options.destination = line.id;
		}

		if (inline.fontFeatures) {
			options.features = inline.fontFeatures;
		}

		var opacity = isNumber(inline.opacity) ? inline.opacity : 1;
		pdfKitDoc.opacity(opacity);
		pdfKitDoc.fill(inline.color || 'black');

		pdfKitDoc._font = inline.font;
		pdfKitDoc.fontSize(inline.fontSize);
		pdfKitDoc.text(inline.text, x + inline.x, y + shiftToBaseline, options);

		if (inline.linkToPage) {
			var _ref = pdfKitDoc.ref({ Type: 'Action', S: 'GoTo', D: [inline.linkToPage, 0, 0] }).end();
			pdfKitDoc.annotate(x + inline.x, y + shiftToBaseline, inline.width, inline.height, {
				Subtype: 'Link',
				Dest: [inline.linkToPage - 1, 'XYZ', null, null, null]
			});
		}

	}

	textDecorator.drawDecorations(line, x, y, pdfKitDoc);
}

function renderWatermark(page, pdfKitDoc) {
	var watermark = page.watermark;

	pdfKitDoc.fill(watermark.color);
	pdfKitDoc.opacity(watermark.opacity);

	pdfKitDoc.save();

	pdfKitDoc.rotate(watermark.angle, { origin: [pdfKitDoc.page.width / 2, pdfKitDoc.page.height / 2] });

	var x = pdfKitDoc.page.width / 2 - watermark._size.size.width / 2;
	var y = pdfKitDoc.page.height / 2 - watermark._size.size.height / 2;

	pdfKitDoc._font = watermark.font;
	pdfKitDoc.fontSize(watermark.fontSize);
	pdfKitDoc.text(watermark.text, x, y, { lineBreak: false });

	pdfKitDoc.restore();
}

function renderVector(vector, pdfKitDoc) {
	//TODO: pdf optimization (there's no need to write all properties everytime)
	pdfKitDoc.lineWidth(vector.lineWidth || 1);
	if (vector.dash) {
		pdfKitDoc.dash(vector.dash.length, { space: vector.dash.space || vector.dash.length, phase: vector.dash.phase || 0 });
	} else {
		pdfKitDoc.undash();
	}
	pdfKitDoc.lineJoin(vector.lineJoin || 'miter');
	pdfKitDoc.lineCap(vector.lineCap || 'butt');

	//TODO: clipping

	var gradient = null;

	switch (vector.type) {
		case 'ellipse':
			pdfKitDoc.ellipse(vector.x, vector.y, vector.r1, vector.r2);

			if (vector.linearGradient) {
				gradient = pdfKitDoc.linearGradient(vector.x - vector.r1, vector.y, vector.x + vector.r1, vector.y);
			}
			break;
		case 'rect':
			if (vector.r) {
				pdfKitDoc.roundedRect(vector.x, vector.y, vector.w, vector.h, vector.r);
			} else {
				pdfKitDoc.rect(vector.x, vector.y, vector.w, vector.h);
			}

			if (vector.linearGradient) {
				gradient = pdfKitDoc.linearGradient(vector.x, vector.y, vector.x + vector.w, vector.y);
			}
			break;
		case 'line':
			pdfKitDoc.moveTo(vector.x1, vector.y1);
			pdfKitDoc.lineTo(vector.x2, vector.y2);
			break;
		case 'polyline':
			if (vector.points.length === 0) {
				break;
			}

			pdfKitDoc.moveTo(vector.points[0].x, vector.points[0].y);
			for (var i = 1, l = vector.points.length; i < l; i++) {
				pdfKitDoc.lineTo(vector.points[i].x, vector.points[i].y);
			}

			if (vector.points.length > 1) {
				var p1 = vector.points[0];
				var pn = vector.points[vector.points.length - 1];

				if (vector.closePath || p1.x === pn.x && p1.y === pn.y) {
					pdfKitDoc.closePath();
				}
			}
			break;
		case 'path':
			pdfKitDoc.path(vector.d);
			break;
	}

	if (vector.linearGradient && gradient) {
		var step = 1 / (vector.linearGradient.length - 1);

		for (var i = 0; i < vector.linearGradient.length; i++) {
			gradient.stop(i * step, vector.linearGradient[i]);
		}

		vector.color = gradient;
	}

	var fillOpacity = isNumber(vector.fillOpacity) ? vector.fillOpacity : 1;
	var strokeOpacity = isNumber(vector.strokeOpacity) ? vector.strokeOpacity : 1;

	if (vector.color && vector.lineColor) {
		pdfKitDoc.fillColor(vector.color, fillOpacity);
		pdfKitDoc.strokeColor(vector.lineColor, strokeOpacity);
		pdfKitDoc.fillAndStroke();
	} else if (vector.color) {
		pdfKitDoc.fillColor(vector.color, fillOpacity);
		pdfKitDoc.fill();
	} else {
		pdfKitDoc.strokeColor(vector.lineColor || 'black', strokeOpacity);
		pdfKitDoc.stroke();
	}
}

function renderImage(image, x, y, pdfKitDoc) {
	var opacity = isNumber(image.opacity) ? image.opacity : 1;
	pdfKitDoc.opacity(opacity);
	pdfKitDoc.image(image.image, image.x, image.y, { width: image._width, height: image._height });
	if (image.link) {
		pdfKitDoc.link(image.x, image.y, image._width, image._height, image.link);
	}
	if (image.linkToPage) {
		pdfKitDoc.ref({ Type: 'Action', S: 'GoTo', D: [image.linkToPage, 0, 0] }).end();
		pdfKitDoc.annotate(image.x, image.y, image._width, image._height, { Subtype: 'Link', Dest: [image.linkToPage - 1, 'XYZ', null, null, null] });
	}
	if (image.linkToDestination) {
		pdfKitDoc.goTo(image.x, image.y, image._width, image._height, image.linkToDestination);
	}
}

function renderSVG(svg, x, y, pdfKitDoc, fontProvider) {
	var options = Object.assign({ width: svg._width, height: svg._height, assumePt: true }, svg.options);
	options.fontCallback = function (family, bold, italic) {
		var fontsFamily = family.split(',').map(function (f) { return f.trim().replace(/('|")/g, ''); });
		var font = findFont(fontProvider.fonts, fontsFamily, svg.font || 'Roboto');

		var fontFile = fontProvider.getFontFile(font, bold, italic);
		if (fontFile === null) {
			var type = fontProvider.getFontType(bold, italic);
			throw new Error('Font \'' + font + '\' in style \'' + type + '\' is not defined in the font section of the document definition.');
		}

		return fontFile;
	};

	getSvgToPDF()(pdfKitDoc, svg.svg, svg.x, svg.y, options);
}

function beginClip(rect, pdfKitDoc) {
	pdfKitDoc.save();
	pdfKitDoc.addContent('' + rect.x + ' ' + rect.y + ' ' + rect.width + ' ' + rect.height + ' re');
	pdfKitDoc.clip();
}

function endClip(pdfKitDoc) {
	pdfKitDoc.restore();
}

module.exports = PdfPrinter;


/***/ }),
/* 306 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


function _interopDefault(ex) {
	return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex;
}

var PdfKit = _interopDefault(__webpack_require__(307));

function getEngineInstance() {
	return PdfKit;
}

function createPdfDocument(options) {
	options = options || {};
	return new PdfKit(options);
}

module.exports = {
	getEngineInstance: getEngineInstance,
	createPdfDocument: createPdfDocument
};


/***/ }),
/* 307 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(Buffer, __dirname) {

__webpack_require__(197);

__webpack_require__(207);

__webpack_require__(208);

__webpack_require__(209);

__webpack_require__(312);

__webpack_require__(211);

__webpack_require__(313);

__webpack_require__(314);

__webpack_require__(213);

__webpack_require__(215);

__webpack_require__(28);

__webpack_require__(219);

__webpack_require__(318);

__webpack_require__(46);

__webpack_require__(319);

__webpack_require__(320);

__webpack_require__(61);

__webpack_require__(157);

__webpack_require__(223);

__webpack_require__(322);

__webpack_require__(324);

__webpack_require__(326);

__webpack_require__(227);

__webpack_require__(327);

__webpack_require__(32);

__webpack_require__(118);

__webpack_require__(229);

__webpack_require__(330);

__webpack_require__(331);

__webpack_require__(159);

__webpack_require__(160);

__webpack_require__(230);

__webpack_require__(231);

__webpack_require__(232);

__webpack_require__(233);

__webpack_require__(334);

__webpack_require__(62);

__webpack_require__(63);

__webpack_require__(64);

__webpack_require__(65);

__webpack_require__(66);

__webpack_require__(67);

__webpack_require__(68);

__webpack_require__(69);

__webpack_require__(70);

__webpack_require__(71);

__webpack_require__(72);

__webpack_require__(73);

__webpack_require__(74);

__webpack_require__(75);

__webpack_require__(76);

__webpack_require__(77);

__webpack_require__(78);

__webpack_require__(79);

__webpack_require__(80);

__webpack_require__(81);

__webpack_require__(82);

__webpack_require__(83);

__webpack_require__(84);

__webpack_require__(85);

__webpack_require__(340);

__webpack_require__(120);

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _stream = _interopRequireDefault(__webpack_require__(121));

var _zlib = _interopRequireDefault(__webpack_require__(244));

var _cryptoJs = _interopRequireDefault(__webpack_require__(362));

var _fontkit = _interopRequireDefault(__webpack_require__(386));

var _events = __webpack_require__(122);

var _linebreak = _interopRequireDefault(__webpack_require__(290));

var _pngJs = _interopRequireDefault(__webpack_require__(531));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var fs = __webpack_require__(53);

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) {
      arr2[i] = arr[i];
    }

    return arr2;
  }
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}
/*
PDFAbstractReference - abstract class for PDF reference
*/


var PDFAbstractReference = /*#__PURE__*/function () {
  function PDFAbstractReference() {
    _classCallCheck(this, PDFAbstractReference);
  }

  _createClass(PDFAbstractReference, [{
    key: "toString",
    value: function toString() {
      throw new Error('Must be implemented by subclasses');
    }
  }]);

  return PDFAbstractReference;
}();

var PDFNameTree = /*#__PURE__*/function () {
  function PDFNameTree() {
    _classCallCheck(this, PDFNameTree);

    this._items = {};
  }

  _createClass(PDFNameTree, [{
    key: "add",
    value: function add(key, val) {
      return this._items[key] = val;
    }
  }, {
    key: "get",
    value: function get(key) {
      return this._items[key];
    }
  }, {
    key: "toString",
    value: function toString() {
      // Needs to be sorted by key
      var sortedKeys = Object.keys(this._items).sort(function (a, b) {
        return a.localeCompare(b);
      });
      var out = ['<<'];

      if (sortedKeys.length > 1) {
        var first = sortedKeys[0],
            last = sortedKeys[sortedKeys.length - 1];
        out.push("  /Limits ".concat(PDFObject.convert([new String(first), new String(last)])));
      }

      out.push('  /Names [');
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = sortedKeys[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var key = _step.value;
          out.push("    ".concat(PDFObject.convert(new String(key)), " ").concat(PDFObject.convert(this._items[key])));
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      out.push(']');
      out.push('>>');
      return out.join('\n');
    }
  }]);

  return PDFNameTree;
}();

var pad = function pad(str, length) {
  return (Array(length + 1).join('0') + str).slice(-length);
};

var escapableRe = /[\n\r\t\b\f\(\)\\]/g;
var escapable = {
  '\n': '\\n',
  '\r': '\\r',
  '\t': '\\t',
  '\b': '\\b',
  '\f': '\\f',
  '\\': '\\\\',
  '(': '\\(',
  ')': '\\)'
}; // Convert little endian UTF-16 to big endian

var swapBytes = function swapBytes(buff) {
  var l = buff.length;

  if (l & 0x01) {
    throw new Error('Buffer length must be even');
  } else {
    for (var i = 0, end = l - 1; i < end; i += 2) {
      var a = buff[i];
      buff[i] = buff[i + 1];
      buff[i + 1] = a;
    }
  }

  return buff;
};

var PDFObject = /*#__PURE__*/function () {
  function PDFObject() {
    _classCallCheck(this, PDFObject);
  }

  _createClass(PDFObject, null, [{
    key: "convert",
    value: function convert(object) {
      var encryptFn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null; // String literals are converted to the PDF name type

      if (typeof object === 'string') {
        return "/".concat(object); // String objects are converted to PDF strings (UTF-16)
      } else if (object instanceof String) {
        var string = object; // Detect if this is a unicode string

        var isUnicode = false;

        for (var i = 0, end = string.length; i < end; i++) {
          if (string.charCodeAt(i) > 0x7f) {
            isUnicode = true;
            break;
          }
        } // If so, encode it as big endian UTF-16


        var stringBuffer;

        if (isUnicode) {
          stringBuffer = swapBytes(Buffer.from("\uFEFF".concat(string), 'utf16le'));
        } else {
          stringBuffer = Buffer.from(string.valueOf(), 'ascii');
        } // Encrypt the string when necessary


        if (encryptFn) {
          string = encryptFn(stringBuffer).toString('binary');
        } else {
          string = stringBuffer.toString('binary');
        } // Escape characters as required by the spec


        string = string.replace(escapableRe, function (c) {
          return escapable[c];
        });
        return "(".concat(string, ")"); // Buffers are converted to PDF hex strings
      } else if (Buffer.isBuffer(object)) {
        return "<".concat(object.toString('hex'), ">");
      } else if (object instanceof PDFAbstractReference || object instanceof PDFNameTree) {
        return object.toString();
      } else if (object instanceof Date) {
        var _string = "D:".concat(pad(object.getUTCFullYear(), 4)) + pad(object.getUTCMonth() + 1, 2) + pad(object.getUTCDate(), 2) + pad(object.getUTCHours(), 2) + pad(object.getUTCMinutes(), 2) + pad(object.getUTCSeconds(), 2) + 'Z'; // Encrypt the string when necessary


        if (encryptFn) {
          _string = encryptFn(new Buffer(_string, 'ascii')).toString('binary'); // Escape characters as required by the spec

          _string = _string.replace(escapableRe, function (c) {
            return escapable[c];
          });
        }

        return "(".concat(_string, ")");
      } else if (Array.isArray(object)) {
        var items = object.map(function (e) {
          return PDFObject.convert(e, encryptFn);
        }).join(' ');
        return "[".concat(items, "]");
      } else if ({}.toString.call(object) === '[object Object]') {
        var out = ['<<'];

        for (var key in object) {
          var val = object[key];
          out.push("/".concat(key, " ").concat(PDFObject.convert(val, encryptFn)));
        }

        out.push('>>');
        return out.join('\n');
      } else if (typeof object === 'number') {
        return PDFObject.number(object);
      } else {
        return "".concat(object);
      }
    }
  }, {
    key: "number",
    value: function number(n) {
      if (n > -1e21 && n < 1e21) {
        return Math.round(n * 1e6) / 1e6;
      }

      throw new Error("unsupported number: ".concat(n));
    }
  }]);

  return PDFObject;
}();

var PDFReference = /*#__PURE__*/function (_PDFAbstractReference) {
  _inherits(PDFReference, _PDFAbstractReference);

  function PDFReference(document, id) {
    var _this;

    var data = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    _classCallCheck(this, PDFReference);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(PDFReference).call(this));
    _this.document = document;
    _this.id = id;
    _this.data = data;
    _this.gen = 0;
    _this.compress = _this.document.compress && !_this.data.Filter;
    _this.uncompressedLength = 0;
    _this.buffer = [];
    return _this;
  }

  _createClass(PDFReference, [{
    key: "write",
    value: function write(chunk) {
      if (!Buffer.isBuffer(chunk)) {
        chunk = new Buffer(chunk + '\n', 'binary');
      }

      this.uncompressedLength += chunk.length;

      if (this.data.Length == null) {
        this.data.Length = 0;
      }

      this.buffer.push(chunk);
      this.data.Length += chunk.length;

      if (this.compress) {
        return this.data.Filter = 'FlateDecode';
      }
    }
  }, {
    key: "end",
    value: function end(chunk) {
      if (chunk) {
        this.write(chunk);
      }

      return this.finalize();
    }
  }, {
    key: "finalize",
    value: function finalize() {
      this.offset = this.document._offset;
      var encryptFn = this.document._security ? this.document._security.getEncryptFn(this.id, this.gen) : null;

      if (this.buffer.length) {
        this.buffer = Buffer.concat(this.buffer);

        if (this.compress) {
          this.buffer = _zlib.default.deflateSync(this.buffer);
        }

        if (encryptFn) {
          this.buffer = encryptFn(this.buffer);
        }

        this.data.Length = this.buffer.length;
      }

      this.document._write("".concat(this.id, " ").concat(this.gen, " obj"));

      this.document._write(PDFObject.convert(this.data, encryptFn));

      if (this.buffer.length) {
        this.document._write('stream');

        this.document._write(this.buffer);

        this.buffer = []; // free up memory

        this.document._write('\nendstream');
      }

      this.document._write('endobj');

      this.document._refEnd(this);
    }
  }, {
    key: "toString",
    value: function toString() {
      return "".concat(this.id, " ").concat(this.gen, " R");
    }
  }]);

  return PDFReference;
}(PDFAbstractReference);
/*
PDFPage - represents a single page in the PDF document
By Devon Govett
*/


var DEFAULT_MARGINS = {
  top: 72,
  left: 72,
  bottom: 72,
  right: 72
};
var SIZES = {
  '4A0': [4767.87, 6740.79],
  '2A0': [3370.39, 4767.87],
  A0: [2383.94, 3370.39],
  A1: [1683.78, 2383.94],
  A2: [1190.55, 1683.78],
  A3: [841.89, 1190.55],
  A4: [595.28, 841.89],
  A5: [419.53, 595.28],
  A6: [297.64, 419.53],
  A7: [209.76, 297.64],
  A8: [147.4, 209.76],
  A9: [104.88, 147.4],
  A10: [73.7, 104.88],
  B0: [2834.65, 4008.19],
  B1: [2004.09, 2834.65],
  B2: [1417.32, 2004.09],
  B3: [1000.63, 1417.32],
  B4: [708.66, 1000.63],
  B5: [498.9, 708.66],
  B6: [354.33, 498.9],
  B7: [249.45, 354.33],
  B8: [175.75, 249.45],
  B9: [124.72, 175.75],
  B10: [87.87, 124.72],
  C0: [2599.37, 3676.54],
  C1: [1836.85, 2599.37],
  C2: [1298.27, 1836.85],
  C3: [918.43, 1298.27],
  C4: [649.13, 918.43],
  C5: [459.21, 649.13],
  C6: [323.15, 459.21],
  C7: [229.61, 323.15],
  C8: [161.57, 229.61],
  C9: [113.39, 161.57],
  C10: [79.37, 113.39],
  RA0: [2437.8, 3458.27],
  RA1: [1729.13, 2437.8],
  RA2: [1218.9, 1729.13],
  RA3: [864.57, 1218.9],
  RA4: [609.45, 864.57],
  SRA0: [2551.18, 3628.35],
  SRA1: [1814.17, 2551.18],
  SRA2: [1275.59, 1814.17],
  SRA3: [907.09, 1275.59],
  SRA4: [637.8, 907.09],
  EXECUTIVE: [521.86, 756.0],
  FOLIO: [612.0, 936.0],
  LEGAL: [612.0, 1008.0],
  LETTER: [612.0, 792.0],
  TABLOID: [792.0, 1224.0]
};

var PDFPage = /*#__PURE__*/function () {
  function PDFPage(document) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    _classCallCheck(this, PDFPage);

    this.document = document;
    this.size = options.size || 'letter';
    this.layout = options.layout || 'portrait'; // process margins

    if (typeof options.margin === 'number') {
      this.margins = {
        top: options.margin,
        left: options.margin,
        bottom: options.margin,
        right: options.margin
      }; // default to 1 inch margins
    } else {
      this.margins = options.margins || DEFAULT_MARGINS;
    } // calculate page dimensions


    var dimensions = Array.isArray(this.size) ? this.size : SIZES[this.size.toUpperCase()];
    this.width = dimensions[this.layout === 'portrait' ? 0 : 1];
    this.height = dimensions[this.layout === 'portrait' ? 1 : 0];
    this.content = this.document.ref(); // Initialize the Font, XObject, and ExtGState dictionaries

    this.resources = this.document.ref({
      ProcSet: ['PDF', 'Text', 'ImageB', 'ImageC', 'ImageI']
    }); // The page dictionary

    this.dictionary = this.document.ref({
      Type: 'Page',
      Parent: this.document._root.data.Pages,
      MediaBox: [0, 0, this.width, this.height],
      Contents: this.content,
      Resources: this.resources
    });
  } // Lazily create these dictionaries


  _createClass(PDFPage, [{
    key: "maxY",
    value: function maxY() {
      return this.height - this.margins.bottom;
    }
  }, {
    key: "write",
    value: function write(chunk) {
      return this.content.write(chunk);
    }
  }, {
    key: "end",
    value: function end() {
      this.dictionary.end();
      this.resources.end();
      return this.content.end();
    }
  }, {
    key: "fonts",
    get: function get() {
      var data = this.resources.data;
      return data.Font != null ? data.Font : data.Font = {};
    }
  }, {
    key: "xobjects",
    get: function get() {
      var data = this.resources.data;
      return data.XObject != null ? data.XObject : data.XObject = {};
    }
  }, {
    key: "ext_gstates",
    get: function get() {
      var data = this.resources.data;
      return data.ExtGState != null ? data.ExtGState : data.ExtGState = {};
    }
  }, {
    key: "patterns",
    get: function get() {
      var data = this.resources.data;
      return data.Pattern != null ? data.Pattern : data.Pattern = {};
    }
  }, {
    key: "annotations",
    get: function get() {
      var data = this.dictionary.data;
      return data.Annots != null ? data.Annots : data.Annots = [];
    }
  }]);

  return PDFPage;
}();
/**
 * Check if value is in a range group.
 * @param {number} value
 * @param {number[]} rangeGroup
 * @returns {boolean}
 */


function inRange(value, rangeGroup) {
  if (value < rangeGroup[0]) return false;
  var startRange = 0;
  var endRange = rangeGroup.length / 2;

  while (startRange <= endRange) {
    var middleRange = Math.floor((startRange + endRange) / 2); // actual array index

    var arrayIndex = middleRange * 2; // Check if value is in range pointed by actual index

    if (value >= rangeGroup[arrayIndex] && value <= rangeGroup[arrayIndex + 1]) {
      return true;
    }

    if (value > rangeGroup[arrayIndex + 1]) {
      // Search Right Side Of Array
      startRange = middleRange + 1;
    } else {
      // Search Left Side Of Array
      endRange = middleRange - 1;
    }
  }

  return false;
}
/* eslint-disable prettier/prettier */

/**
 * A.1 Unassigned code points in Unicode 3.2
 * @link https://tools.ietf.org/html/rfc3454#appendix-A.1
 */


var unassigned_code_points = [0x0221, 0x0221, 0x0234, 0x024f, 0x02ae, 0x02af, 0x02ef, 0x02ff, 0x0350, 0x035f, 0x0370, 0x0373, 0x0376, 0x0379, 0x037b, 0x037d, 0x037f, 0x0383, 0x038b, 0x038b, 0x038d, 0x038d, 0x03a2, 0x03a2, 0x03cf, 0x03cf, 0x03f7, 0x03ff, 0x0487, 0x0487, 0x04cf, 0x04cf, 0x04f6, 0x04f7, 0x04fa, 0x04ff, 0x0510, 0x0530, 0x0557, 0x0558, 0x0560, 0x0560, 0x0588, 0x0588, 0x058b, 0x0590, 0x05a2, 0x05a2, 0x05ba, 0x05ba, 0x05c5, 0x05cf, 0x05eb, 0x05ef, 0x05f5, 0x060b, 0x060d, 0x061a, 0x061c, 0x061e, 0x0620, 0x0620, 0x063b, 0x063f, 0x0656, 0x065f, 0x06ee, 0x06ef, 0x06ff, 0x06ff, 0x070e, 0x070e, 0x072d, 0x072f, 0x074b, 0x077f, 0x07b2, 0x0900, 0x0904, 0x0904, 0x093a, 0x093b, 0x094e, 0x094f, 0x0955, 0x0957, 0x0971, 0x0980, 0x0984, 0x0984, 0x098d, 0x098e, 0x0991, 0x0992, 0x09a9, 0x09a9, 0x09b1, 0x09b1, 0x09b3, 0x09b5, 0x09ba, 0x09bb, 0x09bd, 0x09bd, 0x09c5, 0x09c6, 0x09c9, 0x09ca, 0x09ce, 0x09d6, 0x09d8, 0x09db, 0x09de, 0x09de, 0x09e4, 0x09e5, 0x09fb, 0x0a01, 0x0a03, 0x0a04, 0x0a0b, 0x0a0e, 0x0a11, 0x0a12, 0x0a29, 0x0a29, 0x0a31, 0x0a31, 0x0a34, 0x0a34, 0x0a37, 0x0a37, 0x0a3a, 0x0a3b, 0x0a3d, 0x0a3d, 0x0a43, 0x0a46, 0x0a49, 0x0a4a, 0x0a4e, 0x0a58, 0x0a5d, 0x0a5d, 0x0a5f, 0x0a65, 0x0a75, 0x0a80, 0x0a84, 0x0a84, 0x0a8c, 0x0a8c, 0x0a8e, 0x0a8e, 0x0a92, 0x0a92, 0x0aa9, 0x0aa9, 0x0ab1, 0x0ab1, 0x0ab4, 0x0ab4, 0x0aba, 0x0abb, 0x0ac6, 0x0ac6, 0x0aca, 0x0aca, 0x0ace, 0x0acf, 0x0ad1, 0x0adf, 0x0ae1, 0x0ae5, 0x0af0, 0x0b00, 0x0b04, 0x0b04, 0x0b0d, 0x0b0e, 0x0b11, 0x0b12, 0x0b29, 0x0b29, 0x0b31, 0x0b31, 0x0b34, 0x0b35, 0x0b3a, 0x0b3b, 0x0b44, 0x0b46, 0x0b49, 0x0b4a, 0x0b4e, 0x0b55, 0x0b58, 0x0b5b, 0x0b5e, 0x0b5e, 0x0b62, 0x0b65, 0x0b71, 0x0b81, 0x0b84, 0x0b84, 0x0b8b, 0x0b8d, 0x0b91, 0x0b91, 0x0b96, 0x0b98, 0x0b9b, 0x0b9b, 0x0b9d, 0x0b9d, 0x0ba0, 0x0ba2, 0x0ba5, 0x0ba7, 0x0bab, 0x0bad, 0x0bb6, 0x0bb6, 0x0bba, 0x0bbd, 0x0bc3, 0x0bc5, 0x0bc9, 0x0bc9, 0x0bce, 0x0bd6, 0x0bd8, 0x0be6, 0x0bf3, 0x0c00, 0x0c04, 0x0c04, 0x0c0d, 0x0c0d, 0x0c11, 0x0c11, 0x0c29, 0x0c29, 0x0c34, 0x0c34, 0x0c3a, 0x0c3d, 0x0c45, 0x0c45, 0x0c49, 0x0c49, 0x0c4e, 0x0c54, 0x0c57, 0x0c5f, 0x0c62, 0x0c65, 0x0c70, 0x0c81, 0x0c84, 0x0c84, 0x0c8d, 0x0c8d, 0x0c91, 0x0c91, 0x0ca9, 0x0ca9, 0x0cb4, 0x0cb4, 0x0cba, 0x0cbd, 0x0cc5, 0x0cc5, 0x0cc9, 0x0cc9, 0x0cce, 0x0cd4, 0x0cd7, 0x0cdd, 0x0cdf, 0x0cdf, 0x0ce2, 0x0ce5, 0x0cf0, 0x0d01, 0x0d04, 0x0d04, 0x0d0d, 0x0d0d, 0x0d11, 0x0d11, 0x0d29, 0x0d29, 0x0d3a, 0x0d3d, 0x0d44, 0x0d45, 0x0d49, 0x0d49, 0x0d4e, 0x0d56, 0x0d58, 0x0d5f, 0x0d62, 0x0d65, 0x0d70, 0x0d81, 0x0d84, 0x0d84, 0x0d97, 0x0d99, 0x0db2, 0x0db2, 0x0dbc, 0x0dbc, 0x0dbe, 0x0dbf, 0x0dc7, 0x0dc9, 0x0dcb, 0x0dce, 0x0dd5, 0x0dd5, 0x0dd7, 0x0dd7, 0x0de0, 0x0df1, 0x0df5, 0x0e00, 0x0e3b, 0x0e3e, 0x0e5c, 0x0e80, 0x0e83, 0x0e83, 0x0e85, 0x0e86, 0x0e89, 0x0e89, 0x0e8b, 0x0e8c, 0x0e8e, 0x0e93, 0x0e98, 0x0e98, 0x0ea0, 0x0ea0, 0x0ea4, 0x0ea4, 0x0ea6, 0x0ea6, 0x0ea8, 0x0ea9, 0x0eac, 0x0eac, 0x0eba, 0x0eba, 0x0ebe, 0x0ebf, 0x0ec5, 0x0ec5, 0x0ec7, 0x0ec7, 0x0ece, 0x0ecf, 0x0eda, 0x0edb, 0x0ede, 0x0eff, 0x0f48, 0x0f48, 0x0f6b, 0x0f70, 0x0f8c, 0x0f8f, 0x0f98, 0x0f98, 0x0fbd, 0x0fbd, 0x0fcd, 0x0fce, 0x0fd0, 0x0fff, 0x1022, 0x1022, 0x1028, 0x1028, 0x102b, 0x102b, 0x1033, 0x1035, 0x103a, 0x103f, 0x105a, 0x109f, 0x10c6, 0x10cf, 0x10f9, 0x10fa, 0x10fc, 0x10ff, 0x115a, 0x115e, 0x11a3, 0x11a7, 0x11fa, 0x11ff, 0x1207, 0x1207, 0x1247, 0x1247, 0x1249, 0x1249, 0x124e, 0x124f, 0x1257, 0x1257, 0x1259, 0x1259, 0x125e, 0x125f, 0x1287, 0x1287, 0x1289, 0x1289, 0x128e, 0x128f, 0x12af, 0x12af, 0x12b1, 0x12b1, 0x12b6, 0x12b7, 0x12bf, 0x12bf, 0x12c1, 0x12c1, 0x12c6, 0x12c7, 0x12cf, 0x12cf, 0x12d7, 0x12d7, 0x12ef, 0x12ef, 0x130f, 0x130f, 0x1311, 0x1311, 0x1316, 0x1317, 0x131f, 0x131f, 0x1347, 0x1347, 0x135b, 0x1360, 0x137d, 0x139f, 0x13f5, 0x1400, 0x1677, 0x167f, 0x169d, 0x169f, 0x16f1, 0x16ff, 0x170d, 0x170d, 0x1715, 0x171f, 0x1737, 0x173f, 0x1754, 0x175f, 0x176d, 0x176d, 0x1771, 0x1771, 0x1774, 0x177f, 0x17dd, 0x17df, 0x17ea, 0x17ff, 0x180f, 0x180f, 0x181a, 0x181f, 0x1878, 0x187f, 0x18aa, 0x1dff, 0x1e9c, 0x1e9f, 0x1efa, 0x1eff, 0x1f16, 0x1f17, 0x1f1e, 0x1f1f, 0x1f46, 0x1f47, 0x1f4e, 0x1f4f, 0x1f58, 0x1f58, 0x1f5a, 0x1f5a, 0x1f5c, 0x1f5c, 0x1f5e, 0x1f5e, 0x1f7e, 0x1f7f, 0x1fb5, 0x1fb5, 0x1fc5, 0x1fc5, 0x1fd4, 0x1fd5, 0x1fdc, 0x1fdc, 0x1ff0, 0x1ff1, 0x1ff5, 0x1ff5, 0x1fff, 0x1fff, 0x2053, 0x2056, 0x2058, 0x205e, 0x2064, 0x2069, 0x2072, 0x2073, 0x208f, 0x209f, 0x20b2, 0x20cf, 0x20eb, 0x20ff, 0x213b, 0x213c, 0x214c, 0x2152, 0x2184, 0x218f, 0x23cf, 0x23ff, 0x2427, 0x243f, 0x244b, 0x245f, 0x24ff, 0x24ff, 0x2614, 0x2615, 0x2618, 0x2618, 0x267e, 0x267f, 0x268a, 0x2700, 0x2705, 0x2705, 0x270a, 0x270b, 0x2728, 0x2728, 0x274c, 0x274c, 0x274e, 0x274e, 0x2753, 0x2755, 0x2757, 0x2757, 0x275f, 0x2760, 0x2795, 0x2797, 0x27b0, 0x27b0, 0x27bf, 0x27cf, 0x27ec, 0x27ef, 0x2b00, 0x2e7f, 0x2e9a, 0x2e9a, 0x2ef4, 0x2eff, 0x2fd6, 0x2fef, 0x2ffc, 0x2fff, 0x3040, 0x3040, 0x3097, 0x3098, 0x3100, 0x3104, 0x312d, 0x3130, 0x318f, 0x318f, 0x31b8, 0x31ef, 0x321d, 0x321f, 0x3244, 0x3250, 0x327c, 0x327e, 0x32cc, 0x32cf, 0x32ff, 0x32ff, 0x3377, 0x337a, 0x33de, 0x33df, 0x33ff, 0x33ff, 0x4db6, 0x4dff, 0x9fa6, 0x9fff, 0xa48d, 0xa48f, 0xa4c7, 0xabff, 0xd7a4, 0xd7ff, 0xfa2e, 0xfa2f, 0xfa6b, 0xfaff, 0xfb07, 0xfb12, 0xfb18, 0xfb1c, 0xfb37, 0xfb37, 0xfb3d, 0xfb3d, 0xfb3f, 0xfb3f, 0xfb42, 0xfb42, 0xfb45, 0xfb45, 0xfbb2, 0xfbd2, 0xfd40, 0xfd4f, 0xfd90, 0xfd91, 0xfdc8, 0xfdcf, 0xfdfd, 0xfdff, 0xfe10, 0xfe1f, 0xfe24, 0xfe2f, 0xfe47, 0xfe48, 0xfe53, 0xfe53, 0xfe67, 0xfe67, 0xfe6c, 0xfe6f, 0xfe75, 0xfe75, 0xfefd, 0xfefe, 0xff00, 0xff00, 0xffbf, 0xffc1, 0xffc8, 0xffc9, 0xffd0, 0xffd1, 0xffd8, 0xffd9, 0xffdd, 0xffdf, 0xffe7, 0xffe7, 0xffef, 0xfff8, 0x10000, 0x102ff, 0x1031f, 0x1031f, 0x10324, 0x1032f, 0x1034b, 0x103ff, 0x10426, 0x10427, 0x1044e, 0x1cfff, 0x1d0f6, 0x1d0ff, 0x1d127, 0x1d129, 0x1d1de, 0x1d3ff, 0x1d455, 0x1d455, 0x1d49d, 0x1d49d, 0x1d4a0, 0x1d4a1, 0x1d4a3, 0x1d4a4, 0x1d4a7, 0x1d4a8, 0x1d4ad, 0x1d4ad, 0x1d4ba, 0x1d4ba, 0x1d4bc, 0x1d4bc, 0x1d4c1, 0x1d4c1, 0x1d4c4, 0x1d4c4, 0x1d506, 0x1d506, 0x1d50b, 0x1d50c, 0x1d515, 0x1d515, 0x1d51d, 0x1d51d, 0x1d53a, 0x1d53a, 0x1d53f, 0x1d53f, 0x1d545, 0x1d545, 0x1d547, 0x1d549, 0x1d551, 0x1d551, 0x1d6a4, 0x1d6a7, 0x1d7ca, 0x1d7cd, 0x1d800, 0x1fffd, 0x2a6d7, 0x2f7ff, 0x2fa1e, 0x2fffd, 0x30000, 0x3fffd, 0x40000, 0x4fffd, 0x50000, 0x5fffd, 0x60000, 0x6fffd, 0x70000, 0x7fffd, 0x80000, 0x8fffd, 0x90000, 0x9fffd, 0xa0000, 0xafffd, 0xb0000, 0xbfffd, 0xc0000, 0xcfffd, 0xd0000, 0xdfffd, 0xe0000, 0xe0000, 0xe0002, 0xe001f, 0xe0080, 0xefffd];
/* eslint-enable */

var isUnassignedCodePoint = function isUnassignedCodePoint(character) {
  return inRange(character, unassigned_code_points);
};
/* eslint-disable prettier/prettier */

/**
 * B.1 Commonly mapped to nothing
 * @link https://tools.ietf.org/html/rfc3454#appendix-B.1
 */


var commonly_mapped_to_nothing = [0x00ad, 0x00ad, 0x034f, 0x034f, 0x1806, 0x1806, 0x180b, 0x180b, 0x180c, 0x180c, 0x180d, 0x180d, 0x200b, 0x200b, 0x200c, 0x200c, 0x200d, 0x200d, 0x2060, 0x2060, 0xfe00, 0xfe00, 0xfe01, 0xfe01, 0xfe02, 0xfe02, 0xfe03, 0xfe03, 0xfe04, 0xfe04, 0xfe05, 0xfe05, 0xfe06, 0xfe06, 0xfe07, 0xfe07, 0xfe08, 0xfe08, 0xfe09, 0xfe09, 0xfe0a, 0xfe0a, 0xfe0b, 0xfe0b, 0xfe0c, 0xfe0c, 0xfe0d, 0xfe0d, 0xfe0e, 0xfe0e, 0xfe0f, 0xfe0f, 0xfeff, 0xfeff];
/* eslint-enable */

var isCommonlyMappedToNothing = function isCommonlyMappedToNothing(character) {
  return inRange(character, commonly_mapped_to_nothing);
};
/* eslint-disable prettier/prettier */

/**
 * C.1.2 Non-ASCII space characters
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.1.2
 */


var non_ASCII_space_characters = [0x00a0, 0x00a0
/* NO-BREAK SPACE */
, 0x1680, 0x1680
/* OGHAM SPACE MARK */
, 0x2000, 0x2000
/* EN QUAD */
, 0x2001, 0x2001
/* EM QUAD */
, 0x2002, 0x2002
/* EN SPACE */
, 0x2003, 0x2003
/* EM SPACE */
, 0x2004, 0x2004
/* THREE-PER-EM SPACE */
, 0x2005, 0x2005
/* FOUR-PER-EM SPACE */
, 0x2006, 0x2006
/* SIX-PER-EM SPACE */
, 0x2007, 0x2007
/* FIGURE SPACE */
, 0x2008, 0x2008
/* PUNCTUATION SPACE */
, 0x2009, 0x2009
/* THIN SPACE */
, 0x200a, 0x200a
/* HAIR SPACE */
, 0x200b, 0x200b
/* ZERO WIDTH SPACE */
, 0x202f, 0x202f
/* NARROW NO-BREAK SPACE */
, 0x205f, 0x205f
/* MEDIUM MATHEMATICAL SPACE */
, 0x3000, 0x3000
/* IDEOGRAPHIC SPACE */
];
/* eslint-enable */

var isNonASCIISpaceCharacter = function isNonASCIISpaceCharacter(character) {
  return inRange(character, non_ASCII_space_characters);
};
/* eslint-disable prettier/prettier */


var non_ASCII_controls_characters = [
/**
 * C.2.2 Non-ASCII control characters
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.2.2
 */
0x0080, 0x009f
/* [CONTROL CHARACTERS] */
, 0x06dd, 0x06dd
/* ARABIC END OF AYAH */
, 0x070f, 0x070f
/* SYRIAC ABBREVIATION MARK */
, 0x180e, 0x180e
/* MONGOLIAN VOWEL SEPARATOR */
, 0x200c, 0x200c
/* ZERO WIDTH NON-JOINER */
, 0x200d, 0x200d
/* ZERO WIDTH JOINER */
, 0x2028, 0x2028
/* LINE SEPARATOR */
, 0x2029, 0x2029
/* PARAGRAPH SEPARATOR */
, 0x2060, 0x2060
/* WORD JOINER */
, 0x2061, 0x2061
/* FUNCTION APPLICATION */
, 0x2062, 0x2062
/* INVISIBLE TIMES */
, 0x2063, 0x2063
/* INVISIBLE SEPARATOR */
, 0x206a, 0x206f
/* [CONTROL CHARACTERS] */
, 0xfeff, 0xfeff
/* ZERO WIDTH NO-BREAK SPACE */
, 0xfff9, 0xfffc
/* [CONTROL CHARACTERS] */
, 0x1d173, 0x1d17a
/* [MUSICAL CONTROL CHARACTERS] */
];
var non_character_codepoints = [
/**
 * C.4 Non-character code points
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.4
 */
0xfdd0, 0xfdef
/* [NONCHARACTER CODE POINTS] */
, 0xfffe, 0xffff
/* [NONCHARACTER CODE POINTS] */
, 0x1fffe, 0x1ffff
/* [NONCHARACTER CODE POINTS] */
, 0x2fffe, 0x2ffff
/* [NONCHARACTER CODE POINTS] */
, 0x3fffe, 0x3ffff
/* [NONCHARACTER CODE POINTS] */
, 0x4fffe, 0x4ffff
/* [NONCHARACTER CODE POINTS] */
, 0x5fffe, 0x5ffff
/* [NONCHARACTER CODE POINTS] */
, 0x6fffe, 0x6ffff
/* [NONCHARACTER CODE POINTS] */
, 0x7fffe, 0x7ffff
/* [NONCHARACTER CODE POINTS] */
, 0x8fffe, 0x8ffff
/* [NONCHARACTER CODE POINTS] */
, 0x9fffe, 0x9ffff
/* [NONCHARACTER CODE POINTS] */
, 0xafffe, 0xaffff
/* [NONCHARACTER CODE POINTS] */
, 0xbfffe, 0xbffff
/* [NONCHARACTER CODE POINTS] */
, 0xcfffe, 0xcffff
/* [NONCHARACTER CODE POINTS] */
, 0xdfffe, 0xdffff
/* [NONCHARACTER CODE POINTS] */
, 0xefffe, 0xeffff
/* [NONCHARACTER CODE POINTS] */
, 0x10fffe, 0x10ffff
/* [NONCHARACTER CODE POINTS] */
];
/**
 * 2.3.  Prohibited Output
 */

var prohibited_characters = [
/**
 * C.2.1 ASCII control characters
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.2.1
 */
0, 0x001f
/* [CONTROL CHARACTERS] */
, 0x007f, 0x007f
/* DELETE */
,
/**
 * C.8 Change display properties or are deprecated
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.8
 */
0x0340, 0x0340
/* COMBINING GRAVE TONE MARK */
, 0x0341, 0x0341
/* COMBINING ACUTE TONE MARK */
, 0x200e, 0x200e
/* LEFT-TO-RIGHT MARK */
, 0x200f, 0x200f
/* RIGHT-TO-LEFT MARK */
, 0x202a, 0x202a
/* LEFT-TO-RIGHT EMBEDDING */
, 0x202b, 0x202b
/* RIGHT-TO-LEFT EMBEDDING */
, 0x202c, 0x202c
/* POP DIRECTIONAL FORMATTING */
, 0x202d, 0x202d
/* LEFT-TO-RIGHT OVERRIDE */
, 0x202e, 0x202e
/* RIGHT-TO-LEFT OVERRIDE */
, 0x206a, 0x206a
/* INHIBIT SYMMETRIC SWAPPING */
, 0x206b, 0x206b
/* ACTIVATE SYMMETRIC SWAPPING */
, 0x206c, 0x206c
/* INHIBIT ARABIC FORM SHAPING */
, 0x206d, 0x206d
/* ACTIVATE ARABIC FORM SHAPING */
, 0x206e, 0x206e
/* NATIONAL DIGIT SHAPES */
, 0x206f, 0x206f
/* NOMINAL DIGIT SHAPES */
,
/**
 * C.7 Inappropriate for canonical representation
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.7
 */
0x2ff0, 0x2ffb
/* [IDEOGRAPHIC DESCRIPTION CHARACTERS] */
,
/**
 * C.5 Surrogate codes
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.5
 */
0xd800, 0xdfff,
/**
 * C.3 Private use
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.3
 */
0xe000, 0xf8ff
/* [PRIVATE USE, PLANE 0] */
,
/**
 * C.6 Inappropriate for plain text
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.6
 */
0xfff9, 0xfff9
/* INTERLINEAR ANNOTATION ANCHOR */
, 0xfffa, 0xfffa
/* INTERLINEAR ANNOTATION SEPARATOR */
, 0xfffb, 0xfffb
/* INTERLINEAR ANNOTATION TERMINATOR */
, 0xfffc, 0xfffc
/* OBJECT REPLACEMENT CHARACTER */
, 0xfffd, 0xfffd
/* REPLACEMENT CHARACTER */
,
/**
 * C.9 Tagging characters
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.9
 */
0xe0001, 0xe0001
/* LANGUAGE TAG */
, 0xe0020, 0xe007f
/* [TAGGING CHARACTERS] */
,
/**
 * C.3 Private use
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.3
 */
0xf0000, 0xffffd
/* [PRIVATE USE, PLANE 15] */
, 0x100000, 0x10fffd
/* [PRIVATE USE, PLANE 16] */
];
/* eslint-enable */

var isProhibitedCharacter = function isProhibitedCharacter(character) {
  return inRange(character, non_ASCII_space_characters) || inRange(character, prohibited_characters) || inRange(character, non_ASCII_controls_characters) || inRange(character, non_character_codepoints);
};
/* eslint-disable prettier/prettier */

/**
 * D.1 Characters with bidirectional property "R" or "AL"
 * @link https://tools.ietf.org/html/rfc3454#appendix-D.1
 */


var bidirectional_r_al = [0x05be, 0x05be, 0x05c0, 0x05c0, 0x05c3, 0x05c3, 0x05d0, 0x05ea, 0x05f0, 0x05f4, 0x061b, 0x061b, 0x061f, 0x061f, 0x0621, 0x063a, 0x0640, 0x064a, 0x066d, 0x066f, 0x0671, 0x06d5, 0x06dd, 0x06dd, 0x06e5, 0x06e6, 0x06fa, 0x06fe, 0x0700, 0x070d, 0x0710, 0x0710, 0x0712, 0x072c, 0x0780, 0x07a5, 0x07b1, 0x07b1, 0x200f, 0x200f, 0xfb1d, 0xfb1d, 0xfb1f, 0xfb28, 0xfb2a, 0xfb36, 0xfb38, 0xfb3c, 0xfb3e, 0xfb3e, 0xfb40, 0xfb41, 0xfb43, 0xfb44, 0xfb46, 0xfbb1, 0xfbd3, 0xfd3d, 0xfd50, 0xfd8f, 0xfd92, 0xfdc7, 0xfdf0, 0xfdfc, 0xfe70, 0xfe74, 0xfe76, 0xfefc];
/* eslint-enable */

var isBidirectionalRAL = function isBidirectionalRAL(character) {
  return inRange(character, bidirectional_r_al);
};
/* eslint-disable prettier/prettier */

/**
 * D.2 Characters with bidirectional property "L"
 * @link https://tools.ietf.org/html/rfc3454#appendix-D.2
 */


var bidirectional_l = [0x0041, 0x005a, 0x0061, 0x007a, 0x00aa, 0x00aa, 0x00b5, 0x00b5, 0x00ba, 0x00ba, 0x00c0, 0x00d6, 0x00d8, 0x00f6, 0x00f8, 0x0220, 0x0222, 0x0233, 0x0250, 0x02ad, 0x02b0, 0x02b8, 0x02bb, 0x02c1, 0x02d0, 0x02d1, 0x02e0, 0x02e4, 0x02ee, 0x02ee, 0x037a, 0x037a, 0x0386, 0x0386, 0x0388, 0x038a, 0x038c, 0x038c, 0x038e, 0x03a1, 0x03a3, 0x03ce, 0x03d0, 0x03f5, 0x0400, 0x0482, 0x048a, 0x04ce, 0x04d0, 0x04f5, 0x04f8, 0x04f9, 0x0500, 0x050f, 0x0531, 0x0556, 0x0559, 0x055f, 0x0561, 0x0587, 0x0589, 0x0589, 0x0903, 0x0903, 0x0905, 0x0939, 0x093d, 0x0940, 0x0949, 0x094c, 0x0950, 0x0950, 0x0958, 0x0961, 0x0964, 0x0970, 0x0982, 0x0983, 0x0985, 0x098c, 0x098f, 0x0990, 0x0993, 0x09a8, 0x09aa, 0x09b0, 0x09b2, 0x09b2, 0x09b6, 0x09b9, 0x09be, 0x09c0, 0x09c7, 0x09c8, 0x09cb, 0x09cc, 0x09d7, 0x09d7, 0x09dc, 0x09dd, 0x09df, 0x09e1, 0x09e6, 0x09f1, 0x09f4, 0x09fa, 0x0a05, 0x0a0a, 0x0a0f, 0x0a10, 0x0a13, 0x0a28, 0x0a2a, 0x0a30, 0x0a32, 0x0a33, 0x0a35, 0x0a36, 0x0a38, 0x0a39, 0x0a3e, 0x0a40, 0x0a59, 0x0a5c, 0x0a5e, 0x0a5e, 0x0a66, 0x0a6f, 0x0a72, 0x0a74, 0x0a83, 0x0a83, 0x0a85, 0x0a8b, 0x0a8d, 0x0a8d, 0x0a8f, 0x0a91, 0x0a93, 0x0aa8, 0x0aaa, 0x0ab0, 0x0ab2, 0x0ab3, 0x0ab5, 0x0ab9, 0x0abd, 0x0ac0, 0x0ac9, 0x0ac9, 0x0acb, 0x0acc, 0x0ad0, 0x0ad0, 0x0ae0, 0x0ae0, 0x0ae6, 0x0aef, 0x0b02, 0x0b03, 0x0b05, 0x0b0c, 0x0b0f, 0x0b10, 0x0b13, 0x0b28, 0x0b2a, 0x0b30, 0x0b32, 0x0b33, 0x0b36, 0x0b39, 0x0b3d, 0x0b3e, 0x0b40, 0x0b40, 0x0b47, 0x0b48, 0x0b4b, 0x0b4c, 0x0b57, 0x0b57, 0x0b5c, 0x0b5d, 0x0b5f, 0x0b61, 0x0b66, 0x0b70, 0x0b83, 0x0b83, 0x0b85, 0x0b8a, 0x0b8e, 0x0b90, 0x0b92, 0x0b95, 0x0b99, 0x0b9a, 0x0b9c, 0x0b9c, 0x0b9e, 0x0b9f, 0x0ba3, 0x0ba4, 0x0ba8, 0x0baa, 0x0bae, 0x0bb5, 0x0bb7, 0x0bb9, 0x0bbe, 0x0bbf, 0x0bc1, 0x0bc2, 0x0bc6, 0x0bc8, 0x0bca, 0x0bcc, 0x0bd7, 0x0bd7, 0x0be7, 0x0bf2, 0x0c01, 0x0c03, 0x0c05, 0x0c0c, 0x0c0e, 0x0c10, 0x0c12, 0x0c28, 0x0c2a, 0x0c33, 0x0c35, 0x0c39, 0x0c41, 0x0c44, 0x0c60, 0x0c61, 0x0c66, 0x0c6f, 0x0c82, 0x0c83, 0x0c85, 0x0c8c, 0x0c8e, 0x0c90, 0x0c92, 0x0ca8, 0x0caa, 0x0cb3, 0x0cb5, 0x0cb9, 0x0cbe, 0x0cbe, 0x0cc0, 0x0cc4, 0x0cc7, 0x0cc8, 0x0cca, 0x0ccb, 0x0cd5, 0x0cd6, 0x0cde, 0x0cde, 0x0ce0, 0x0ce1, 0x0ce6, 0x0cef, 0x0d02, 0x0d03, 0x0d05, 0x0d0c, 0x0d0e, 0x0d10, 0x0d12, 0x0d28, 0x0d2a, 0x0d39, 0x0d3e, 0x0d40, 0x0d46, 0x0d48, 0x0d4a, 0x0d4c, 0x0d57, 0x0d57, 0x0d60, 0x0d61, 0x0d66, 0x0d6f, 0x0d82, 0x0d83, 0x0d85, 0x0d96, 0x0d9a, 0x0db1, 0x0db3, 0x0dbb, 0x0dbd, 0x0dbd, 0x0dc0, 0x0dc6, 0x0dcf, 0x0dd1, 0x0dd8, 0x0ddf, 0x0df2, 0x0df4, 0x0e01, 0x0e30, 0x0e32, 0x0e33, 0x0e40, 0x0e46, 0x0e4f, 0x0e5b, 0x0e81, 0x0e82, 0x0e84, 0x0e84, 0x0e87, 0x0e88, 0x0e8a, 0x0e8a, 0x0e8d, 0x0e8d, 0x0e94, 0x0e97, 0x0e99, 0x0e9f, 0x0ea1, 0x0ea3, 0x0ea5, 0x0ea5, 0x0ea7, 0x0ea7, 0x0eaa, 0x0eab, 0x0ead, 0x0eb0, 0x0eb2, 0x0eb3, 0x0ebd, 0x0ebd, 0x0ec0, 0x0ec4, 0x0ec6, 0x0ec6, 0x0ed0, 0x0ed9, 0x0edc, 0x0edd, 0x0f00, 0x0f17, 0x0f1a, 0x0f34, 0x0f36, 0x0f36, 0x0f38, 0x0f38, 0x0f3e, 0x0f47, 0x0f49, 0x0f6a, 0x0f7f, 0x0f7f, 0x0f85, 0x0f85, 0x0f88, 0x0f8b, 0x0fbe, 0x0fc5, 0x0fc7, 0x0fcc, 0x0fcf, 0x0fcf, 0x1000, 0x1021, 0x1023, 0x1027, 0x1029, 0x102a, 0x102c, 0x102c, 0x1031, 0x1031, 0x1038, 0x1038, 0x1040, 0x1057, 0x10a0, 0x10c5, 0x10d0, 0x10f8, 0x10fb, 0x10fb, 0x1100, 0x1159, 0x115f, 0x11a2, 0x11a8, 0x11f9, 0x1200, 0x1206, 0x1208, 0x1246, 0x1248, 0x1248, 0x124a, 0x124d, 0x1250, 0x1256, 0x1258, 0x1258, 0x125a, 0x125d, 0x1260, 0x1286, 0x1288, 0x1288, 0x128a, 0x128d, 0x1290, 0x12ae, 0x12b0, 0x12b0, 0x12b2, 0x12b5, 0x12b8, 0x12be, 0x12c0, 0x12c0, 0x12c2, 0x12c5, 0x12c8, 0x12ce, 0x12d0, 0x12d6, 0x12d8, 0x12ee, 0x12f0, 0x130e, 0x1310, 0x1310, 0x1312, 0x1315, 0x1318, 0x131e, 0x1320, 0x1346, 0x1348, 0x135a, 0x1361, 0x137c, 0x13a0, 0x13f4, 0x1401, 0x1676, 0x1681, 0x169a, 0x16a0, 0x16f0, 0x1700, 0x170c, 0x170e, 0x1711, 0x1720, 0x1731, 0x1735, 0x1736, 0x1740, 0x1751, 0x1760, 0x176c, 0x176e, 0x1770, 0x1780, 0x17b6, 0x17be, 0x17c5, 0x17c7, 0x17c8, 0x17d4, 0x17da, 0x17dc, 0x17dc, 0x17e0, 0x17e9, 0x1810, 0x1819, 0x1820, 0x1877, 0x1880, 0x18a8, 0x1e00, 0x1e9b, 0x1ea0, 0x1ef9, 0x1f00, 0x1f15, 0x1f18, 0x1f1d, 0x1f20, 0x1f45, 0x1f48, 0x1f4d, 0x1f50, 0x1f57, 0x1f59, 0x1f59, 0x1f5b, 0x1f5b, 0x1f5d, 0x1f5d, 0x1f5f, 0x1f7d, 0x1f80, 0x1fb4, 0x1fb6, 0x1fbc, 0x1fbe, 0x1fbe, 0x1fc2, 0x1fc4, 0x1fc6, 0x1fcc, 0x1fd0, 0x1fd3, 0x1fd6, 0x1fdb, 0x1fe0, 0x1fec, 0x1ff2, 0x1ff4, 0x1ff6, 0x1ffc, 0x200e, 0x200e, 0x2071, 0x2071, 0x207f, 0x207f, 0x2102, 0x2102, 0x2107, 0x2107, 0x210a, 0x2113, 0x2115, 0x2115, 0x2119, 0x211d, 0x2124, 0x2124, 0x2126, 0x2126, 0x2128, 0x2128, 0x212a, 0x212d, 0x212f, 0x2131, 0x2133, 0x2139, 0x213d, 0x213f, 0x2145, 0x2149, 0x2160, 0x2183, 0x2336, 0x237a, 0x2395, 0x2395, 0x249c, 0x24e9, 0x3005, 0x3007, 0x3021, 0x3029, 0x3031, 0x3035, 0x3038, 0x303c, 0x3041, 0x3096, 0x309d, 0x309f, 0x30a1, 0x30fa, 0x30fc, 0x30ff, 0x3105, 0x312c, 0x3131, 0x318e, 0x3190, 0x31b7, 0x31f0, 0x321c, 0x3220, 0x3243, 0x3260, 0x327b, 0x327f, 0x32b0, 0x32c0, 0x32cb, 0x32d0, 0x32fe, 0x3300, 0x3376, 0x337b, 0x33dd, 0x33e0, 0x33fe, 0x3400, 0x4db5, 0x4e00, 0x9fa5, 0xa000, 0xa48c, 0xac00, 0xd7a3, 0xd800, 0xfa2d, 0xfa30, 0xfa6a, 0xfb00, 0xfb06, 0xfb13, 0xfb17, 0xff21, 0xff3a, 0xff41, 0xff5a, 0xff66, 0xffbe, 0xffc2, 0xffc7, 0xffca, 0xffcf, 0xffd2, 0xffd7, 0xffda, 0xffdc, 0x10300, 0x1031e, 0x10320, 0x10323, 0x10330, 0x1034a, 0x10400, 0x10425, 0x10428, 0x1044d, 0x1d000, 0x1d0f5, 0x1d100, 0x1d126, 0x1d12a, 0x1d166, 0x1d16a, 0x1d172, 0x1d183, 0x1d184, 0x1d18c, 0x1d1a9, 0x1d1ae, 0x1d1dd, 0x1d400, 0x1d454, 0x1d456, 0x1d49c, 0x1d49e, 0x1d49f, 0x1d4a2, 0x1d4a2, 0x1d4a5, 0x1d4a6, 0x1d4a9, 0x1d4ac, 0x1d4ae, 0x1d4b9, 0x1d4bb, 0x1d4bb, 0x1d4bd, 0x1d4c0, 0x1d4c2, 0x1d4c3, 0x1d4c5, 0x1d505, 0x1d507, 0x1d50a, 0x1d50d, 0x1d514, 0x1d516, 0x1d51c, 0x1d51e, 0x1d539, 0x1d53b, 0x1d53e, 0x1d540, 0x1d544, 0x1d546, 0x1d546, 0x1d54a, 0x1d550, 0x1d552, 0x1d6a3, 0x1d6a8, 0x1d7c9, 0x20000, 0x2a6d6, 0x2f800, 0x2fa1d, 0xf0000, 0xffffd, 0x100000, 0x10fffd];
/* eslint-enable */

var isBidirectionalL = function isBidirectionalL(character) {
  return inRange(character, bidirectional_l);
};
/**
 * non-ASCII space characters [StringPrep, C.1.2] that can be
 * mapped to SPACE (U+0020)
 */


var mapping2space = isNonASCIISpaceCharacter;
/**
 * the "commonly mapped to nothing" characters [StringPrep, B.1]
 * that can be mapped to nothing.
 */

var mapping2nothing = isCommonlyMappedToNothing; // utils

var getCodePoint = function getCodePoint(character) {
  return character.codePointAt(0);
};

var first = function first(x) {
  return x[0];
};

var last = function last(x) {
  return x[x.length - 1];
};
/**
 * Convert provided string into an array of Unicode Code Points.
 * Based on https://stackoverflow.com/a/21409165/1556249
 * and https://www.npmjs.com/package/code-point-at.
 * @param {string} input
 * @returns {number[]}
 */


function toCodePoints(input) {
  var codepoints = [];
  var size = input.length;

  for (var i = 0; i < size; i += 1) {
    var before = input.charCodeAt(i);

    if (before >= 0xd800 && before <= 0xdbff && size > i + 1) {
      var next = input.charCodeAt(i + 1);

      if (next >= 0xdc00 && next <= 0xdfff) {
        codepoints.push((before - 0xd800) * 0x400 + next - 0xdc00 + 0x10000);
        i += 1;
        continue;
      }
    }

    codepoints.push(before);
  }

  return codepoints;
}
/**
 * SASLprep.
 * @param {string} input
 * @param {Object} opts
 * @param {boolean} opts.allowUnassigned
 * @returns {string}
 */


function saslprep(input) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (typeof input !== 'string') {
    throw new TypeError('Expected string.');
  }

  if (input.length === 0) {
    return '';
  } // 1. Map


  var mapped_input = toCodePoints(input) // 1.1 mapping to space
  .map(function (character) {
    return mapping2space(character) ? 0x20 : character;
  }) // 1.2 mapping to nothing
  .filter(function (character) {
    return !mapping2nothing(character);
  }); // 2. Normalize

  var normalized_input = String.fromCodePoint.apply(null, mapped_input).normalize('NFKC');
  var normalized_map = toCodePoints(normalized_input); // 3. Prohibit

  var hasProhibited = normalized_map.some(isProhibitedCharacter);

  if (hasProhibited) {
    throw new Error('Prohibited character, see https://tools.ietf.org/html/rfc4013#section-2.3');
  } // Unassigned Code Points


  if (opts.allowUnassigned !== true) {
    var hasUnassigned = normalized_map.some(isUnassignedCodePoint);

    if (hasUnassigned) {
      throw new Error('Unassigned code point, see https://tools.ietf.org/html/rfc4013#section-2.5');
    }
  } // 4. check bidi


  var hasBidiRAL = normalized_map.some(isBidirectionalRAL);
  var hasBidiL = normalized_map.some(isBidirectionalL); // 4.1 If a string contains any RandALCat character, the string MUST NOT
  // contain any LCat character.

  if (hasBidiRAL && hasBidiL) {
    throw new Error('String must not contain RandALCat and LCat at the same time,' + ' see https://tools.ietf.org/html/rfc3454#section-6');
  }
  /**
   * 4.2 If a string contains any RandALCat character, a RandALCat
   * character MUST be the first character of the string, and a
   * RandALCat character MUST be the last character of the string.
   */


  var isFirstBidiRAL = isBidirectionalRAL(getCodePoint(first(normalized_input)));
  var isLastBidiRAL = isBidirectionalRAL(getCodePoint(last(normalized_input)));

  if (hasBidiRAL && !(isFirstBidiRAL && isLastBidiRAL)) {
    throw new Error('Bidirectional RandALCat character must be the first and the last' + ' character of the string, see https://tools.ietf.org/html/rfc3454#section-6');
  }

  return normalized_input;
}

var PDFSecurity = /*#__PURE__*/function () {
  _createClass(PDFSecurity, null, [{
    key: "generateFileID",
    value: function generateFileID() {
      var info = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var infoStr = "".concat(info.CreationDate.getTime(), "\n");

      for (var key in info) {
        if (!info.hasOwnProperty(key)) {
          continue;
        }

        infoStr += "".concat(key, ": ").concat(info[key], "\n");
      }

      return wordArrayToBuffer(_cryptoJs.default.MD5(infoStr));
    }
  }, {
    key: "generateRandomWordArray",
    value: function generateRandomWordArray(bytes) {
      return _cryptoJs.default.lib.WordArray.random(bytes);
    }
  }, {
    key: "create",
    value: function create(document) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      if (!options.ownerPassword && !options.userPassword) {
        return null;
      }

      return new PDFSecurity(document, options);
    }
  }]);

  function PDFSecurity(document) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    _classCallCheck(this, PDFSecurity);

    if (!options.ownerPassword && !options.userPassword) {
      throw new Error('None of owner password and user password is defined.');
    }

    this.document = document;

    this._setupEncryption(options);
  }

  _createClass(PDFSecurity, [{
    key: "_setupEncryption",
    value: function _setupEncryption(options) {
      switch (options.pdfVersion) {
        case '1.4':
        case '1.5':
          this.version = 2;
          break;

        case '1.6':
        case '1.7':
          this.version = 4;
          break;

        case '1.7ext3':
          this.version = 5;
          break;

        default:
          this.version = 1;
          break;
      }

      var encDict = {
        Filter: 'Standard'
      };

      switch (this.version) {
        case 1:
        case 2:
        case 4:
          this._setupEncryptionV1V2V4(this.version, encDict, options);

          break;

        case 5:
          this._setupEncryptionV5(encDict, options);

          break;
      }

      this.dictionary = this.document.ref(encDict);
    }
  }, {
    key: "_setupEncryptionV1V2V4",
    value: function _setupEncryptionV1V2V4(v, encDict, options) {
      var r, permissions;

      switch (v) {
        case 1:
          r = 2;
          this.keyBits = 40;
          permissions = getPermissionsR2(options.permissions);
          break;

        case 2:
          r = 3;
          this.keyBits = 128;
          permissions = getPermissionsR3(options.permissions);
          break;

        case 4:
          r = 4;
          this.keyBits = 128;
          permissions = getPermissionsR3(options.permissions);
          break;
      }

      var paddedUserPassword = processPasswordR2R3R4(options.userPassword);
      var paddedOwnerPassword = options.ownerPassword ? processPasswordR2R3R4(options.ownerPassword) : paddedUserPassword;
      var ownerPasswordEntry = getOwnerPasswordR2R3R4(r, this.keyBits, paddedUserPassword, paddedOwnerPassword);
      this.encryptionKey = getEncryptionKeyR2R3R4(r, this.keyBits, this.document._id, paddedUserPassword, ownerPasswordEntry, permissions);
      var userPasswordEntry;

      if (r === 2) {
        userPasswordEntry = getUserPasswordR2(this.encryptionKey);
      } else {
        userPasswordEntry = getUserPasswordR3R4(this.document._id, this.encryptionKey);
      }

      encDict.V = v;

      if (v >= 2) {
        encDict.Length = this.keyBits;
      }

      if (v === 4) {
        encDict.CF = {
          StdCF: {
            AuthEvent: 'DocOpen',
            CFM: 'AESV2',
            Length: this.keyBits / 8
          }
        };
        encDict.StmF = 'StdCF';
        encDict.StrF = 'StdCF';
      }

      encDict.R = r;
      encDict.O = wordArrayToBuffer(ownerPasswordEntry);
      encDict.U = wordArrayToBuffer(userPasswordEntry);
      encDict.P = permissions;
    }
  }, {
    key: "_setupEncryptionV5",
    value: function _setupEncryptionV5(encDict, options) {
      this.keyBits = 256;
      var permissions = getPermissionsR3(options);
      var processedUserPassword = processPasswordR5(options.userPassword);
      var processedOwnerPassword = options.ownerPassword ? processPasswordR5(options.ownerPassword) : processedUserPassword;
      this.encryptionKey = getEncryptionKeyR5(PDFSecurity.generateRandomWordArray);
      var userPasswordEntry = getUserPasswordR5(processedUserPassword, PDFSecurity.generateRandomWordArray);

      var userKeySalt = _cryptoJs.default.lib.WordArray.create(userPasswordEntry.words.slice(10, 12), 8);

      var userEncryptionKeyEntry = getUserEncryptionKeyR5(processedUserPassword, userKeySalt, this.encryptionKey);
      var ownerPasswordEntry = getOwnerPasswordR5(processedOwnerPassword, userPasswordEntry, PDFSecurity.generateRandomWordArray);

      var ownerKeySalt = _cryptoJs.default.lib.WordArray.create(ownerPasswordEntry.words.slice(10, 12), 8);

      var ownerEncryptionKeyEntry = getOwnerEncryptionKeyR5(processedOwnerPassword, ownerKeySalt, userPasswordEntry, this.encryptionKey);
      var permsEntry = getEncryptedPermissionsR5(permissions, this.encryptionKey, PDFSecurity.generateRandomWordArray);
      encDict.V = 5;
      encDict.Length = this.keyBits;
      encDict.CF = {
        StdCF: {
          AuthEvent: 'DocOpen',
          CFM: 'AESV3',
          Length: this.keyBits / 8
        }
      };
      encDict.StmF = 'StdCF';
      encDict.StrF = 'StdCF';
      encDict.R = 5;
      encDict.O = wordArrayToBuffer(ownerPasswordEntry);
      encDict.OE = wordArrayToBuffer(ownerEncryptionKeyEntry);
      encDict.U = wordArrayToBuffer(userPasswordEntry);
      encDict.UE = wordArrayToBuffer(userEncryptionKeyEntry);
      encDict.P = permissions;
      encDict.Perms = wordArrayToBuffer(permsEntry);
    }
  }, {
    key: "getEncryptFn",
    value: function getEncryptFn(obj, gen) {
      var digest;

      if (this.version < 5) {
        digest = this.encryptionKey.clone().concat(_cryptoJs.default.lib.WordArray.create([(obj & 0xff) << 24 | (obj & 0xff00) << 8 | obj >> 8 & 0xff00 | gen & 0xff, (gen & 0xff00) << 16], 5));
      }

      if (this.version === 1 || this.version === 2) {
        var _key = _cryptoJs.default.MD5(digest);

        _key.sigBytes = Math.min(16, this.keyBits / 8 + 5);
        return function (buffer) {
          return wordArrayToBuffer(_cryptoJs.default.RC4.encrypt(_cryptoJs.default.lib.WordArray.create(buffer), _key).ciphertext);
        };
      }

      var key;

      if (this.version === 4) {
        key = _cryptoJs.default.MD5(digest.concat(_cryptoJs.default.lib.WordArray.create([0x73416c54], 4)));
      } else {
        key = this.encryptionKey;
      }

      var iv = PDFSecurity.generateRandomWordArray(16);
      var options = {
        mode: _cryptoJs.default.mode.CBC,
        padding: _cryptoJs.default.pad.Pkcs7,
        iv: iv
      };
      return function (buffer) {
        return wordArrayToBuffer(iv.clone().concat(_cryptoJs.default.AES.encrypt(_cryptoJs.default.lib.WordArray.create(buffer), key, options).ciphertext));
      };
    }
  }, {
    key: "end",
    value: function end() {
      this.dictionary.end();
    }
  }]);

  return PDFSecurity;
}();

function getPermissionsR2() {
  var permissionObject = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var permissions = 0xffffffc0 >> 0;

  if (permissionObject.printing) {
    permissions |= 4;
  }

  if (permissionObject.modifying) {
    permissions |= 8;
  }

  if (permissionObject.copying) {
    permissions |= 16;
  }

  if (permissionObject.annotating) {
    permissions |= 32;
  }

  return permissions;
}

function getPermissionsR3() {
  var permissionObject = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var permissions = 0xfffff0c0 >> 0;

  if (permissionObject.printing === 'lowResolution') {
    permissions |= 4;
  }

  if (permissionObject.printing === 'highResolution') {
    permissions |= 2052;
  }

  if (permissionObject.modifying) {
    permissions |= 8;
  }

  if (permissionObject.copying) {
    permissions |= 16;
  }

  if (permissionObject.annotating) {
    permissions |= 32;
  }

  if (permissionObject.fillingForms) {
    permissions |= 256;
  }

  if (permissionObject.contentAccessibility) {
    permissions |= 512;
  }

  if (permissionObject.documentAssembly) {
    permissions |= 1024;
  }

  return permissions;
}

function getUserPasswordR2(encryptionKey) {
  return _cryptoJs.default.RC4.encrypt(processPasswordR2R3R4(), encryptionKey).ciphertext;
}

function getUserPasswordR3R4(documentId, encryptionKey) {
  var key = encryptionKey.clone();

  var cipher = _cryptoJs.default.MD5(processPasswordR2R3R4().concat(_cryptoJs.default.lib.WordArray.create(documentId)));

  for (var i = 0; i < 20; i++) {
    var xorRound = Math.ceil(key.sigBytes / 4);

    for (var j = 0; j < xorRound; j++) {
      key.words[j] = encryptionKey.words[j] ^ (i | i << 8 | i << 16 | i << 24);
    }

    cipher = _cryptoJs.default.RC4.encrypt(cipher, key).ciphertext;
  }

  return cipher.concat(_cryptoJs.default.lib.WordArray.create(null, 16));
}

function getOwnerPasswordR2R3R4(r, keyBits, paddedUserPassword, paddedOwnerPassword) {
  var digest = paddedOwnerPassword;
  var round = r >= 3 ? 51 : 1;

  for (var i = 0; i < round; i++) {
    digest = _cryptoJs.default.MD5(digest);
  }

  var key = digest.clone();
  key.sigBytes = keyBits / 8;
  var cipher = paddedUserPassword;
  round = r >= 3 ? 20 : 1;

  for (var _i = 0; _i < round; _i++) {
    var xorRound = Math.ceil(key.sigBytes / 4);

    for (var j = 0; j < xorRound; j++) {
      key.words[j] = digest.words[j] ^ (_i | _i << 8 | _i << 16 | _i << 24);
    }

    cipher = _cryptoJs.default.RC4.encrypt(cipher, key).ciphertext;
  }

  return cipher;
}

function getEncryptionKeyR2R3R4(r, keyBits, documentId, paddedUserPassword, ownerPasswordEntry, permissions) {
  var key = paddedUserPassword.clone().concat(ownerPasswordEntry).concat(_cryptoJs.default.lib.WordArray.create([lsbFirstWord(permissions)], 4)).concat(_cryptoJs.default.lib.WordArray.create(documentId));
  var round = r >= 3 ? 51 : 1;

  for (var i = 0; i < round; i++) {
    key = _cryptoJs.default.MD5(key);
    key.sigBytes = keyBits / 8;
  }

  return key;
}

function getUserPasswordR5(processedUserPassword, generateRandomWordArray) {
  var validationSalt = generateRandomWordArray(8);
  var keySalt = generateRandomWordArray(8);
  return _cryptoJs.default.SHA256(processedUserPassword.clone().concat(validationSalt)).concat(validationSalt).concat(keySalt);
}

function getUserEncryptionKeyR5(processedUserPassword, userKeySalt, encryptionKey) {
  var key = _cryptoJs.default.SHA256(processedUserPassword.clone().concat(userKeySalt));

  var options = {
    mode: _cryptoJs.default.mode.CBC,
    padding: _cryptoJs.default.pad.NoPadding,
    iv: _cryptoJs.default.lib.WordArray.create(null, 16)
  };
  return _cryptoJs.default.AES.encrypt(encryptionKey, key, options).ciphertext;
}

function getOwnerPasswordR5(processedOwnerPassword, userPasswordEntry, generateRandomWordArray) {
  var validationSalt = generateRandomWordArray(8);
  var keySalt = generateRandomWordArray(8);
  return _cryptoJs.default.SHA256(processedOwnerPassword.clone().concat(validationSalt).concat(userPasswordEntry)).concat(validationSalt).concat(keySalt);
}

function getOwnerEncryptionKeyR5(processedOwnerPassword, ownerKeySalt, userPasswordEntry, encryptionKey) {
  var key = _cryptoJs.default.SHA256(processedOwnerPassword.clone().concat(ownerKeySalt).concat(userPasswordEntry));

  var options = {
    mode: _cryptoJs.default.mode.CBC,
    padding: _cryptoJs.default.pad.NoPadding,
    iv: _cryptoJs.default.lib.WordArray.create(null, 16)
  };
  return _cryptoJs.default.AES.encrypt(encryptionKey, key, options).ciphertext;
}

function getEncryptionKeyR5(generateRandomWordArray) {
  return generateRandomWordArray(32);
}

function getEncryptedPermissionsR5(permissions, encryptionKey, generateRandomWordArray) {
  var cipher = _cryptoJs.default.lib.WordArray.create([lsbFirstWord(permissions), 0xffffffff, 0x54616462], 12).concat(generateRandomWordArray(4));

  var options = {
    mode: _cryptoJs.default.mode.ECB,
    padding: _cryptoJs.default.pad.NoPadding
  };
  return _cryptoJs.default.AES.encrypt(cipher, encryptionKey, options).ciphertext;
}

function processPasswordR2R3R4() {
  var password = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var out = new Buffer(32);
  var length = password.length;
  var index = 0;

  while (index < length && index < 32) {
    var code = password.charCodeAt(index);

    if (code > 0xff) {
      throw new Error('Password contains one or more invalid characters.');
    }

    out[index] = code;
    index++;
  }

  while (index < 32) {
    out[index] = PASSWORD_PADDING[index - length];
    index++;
  }

  return _cryptoJs.default.lib.WordArray.create(out);
}

function processPasswordR5() {
  var password = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  password = unescape(encodeURIComponent(saslprep(password)));
  var length = Math.min(127, password.length);
  var out = new Buffer(length);

  for (var i = 0; i < length; i++) {
    out[i] = password.charCodeAt(i);
  }

  return _cryptoJs.default.lib.WordArray.create(out);
}

function lsbFirstWord(data) {
  return (data & 0xff) << 24 | (data & 0xff00) << 8 | data >> 8 & 0xff00 | data >> 24 & 0xff;
}

function wordArrayToBuffer(wordArray) {
  var byteArray = [];

  for (var i = 0; i < wordArray.sigBytes; i++) {
    byteArray.push(wordArray.words[Math.floor(i / 4)] >> 8 * (3 - i % 4) & 0xff);
  }

  return Buffer.from(byteArray);
}

var PASSWORD_PADDING = [0x28, 0xbf, 0x4e, 0x5e, 0x4e, 0x75, 0x8a, 0x41, 0x64, 0x00, 0x4e, 0x56, 0xff, 0xfa, 0x01, 0x08, 0x2e, 0x2e, 0x00, 0xb6, 0xd0, 0x68, 0x3e, 0x80, 0x2f, 0x0c, 0xa9, 0xfe, 0x64, 0x53, 0x69, 0x7a];
var number = PDFObject.number;

var PDFGradient = /*#__PURE__*/function () {
  function PDFGradient(doc) {
    _classCallCheck(this, PDFGradient);

    this.doc = doc;
    this.stops = [];
    this.embedded = false;
    this.transform = [1, 0, 0, 1, 0, 0];
  }

  _createClass(PDFGradient, [{
    key: "stop",
    value: function stop(pos, color, opacity) {
      if (opacity == null) {
        opacity = 1;
      }

      color = this.doc._normalizeColor(color);

      if (this.stops.length === 0) {
        if (color.length === 3) {
          this._colorSpace = 'DeviceRGB';
        } else if (color.length === 4) {
          this._colorSpace = 'DeviceCMYK';
        } else if (color.length === 1) {
          this._colorSpace = 'DeviceGray';
        } else {
          throw new Error('Unknown color space');
        }
      } else if (this._colorSpace === 'DeviceRGB' && color.length !== 3 || this._colorSpace === 'DeviceCMYK' && color.length !== 4 || this._colorSpace === 'DeviceGray' && color.length !== 1) {
        throw new Error('All gradient stops must use the same color space');
      }

      opacity = Math.max(0, Math.min(1, opacity));
      this.stops.push([pos, color, opacity]);
      return this;
    }
  }, {
    key: "setTransform",
    value: function setTransform(m11, m12, m21, m22, dx, dy) {
      this.transform = [m11, m12, m21, m22, dx, dy];
      return this;
    }
  }, {
    key: "embed",
    value: function embed(m) {
      var fn;
      var stopsLength = this.stops.length;

      if (stopsLength === 0) {
        return;
      }

      this.embedded = true;
      this.matrix = m; // if the last stop comes before 100%, add a copy at 100%

      var last = this.stops[stopsLength - 1];

      if (last[0] < 1) {
        this.stops.push([1, last[1], last[2]]);
      }

      var bounds = [];
      var encode = [];
      var stops = [];

      for (var i = 0; i < stopsLength - 1; i++) {
        encode.push(0, 1);

        if (i + 2 !== stopsLength) {
          bounds.push(this.stops[i + 1][0]);
        }

        fn = this.doc.ref({
          FunctionType: 2,
          Domain: [0, 1],
          C0: this.stops[i + 0][1],
          C1: this.stops[i + 1][1],
          N: 1
        });
        stops.push(fn);
        fn.end();
      } // if there are only two stops, we don't need a stitching function


      if (stopsLength === 1) {
        fn = stops[0];
      } else {
        fn = this.doc.ref({
          FunctionType: 3,
          // stitching function
          Domain: [0, 1],
          Functions: stops,
          Bounds: bounds,
          Encode: encode
        });
        fn.end();
      }

      this.id = "Sh".concat(++this.doc._gradCount);
      var shader = this.shader(fn);
      shader.end();
      var pattern = this.doc.ref({
        Type: 'Pattern',
        PatternType: 2,
        Shading: shader,
        Matrix: this.matrix.map(number)
      });
      pattern.end();

      if (this.stops.some(function (stop) {
        return stop[2] < 1;
      })) {
        var grad = this.opacityGradient();
        grad._colorSpace = 'DeviceGray';
        var _iteratorNormalCompletion = true;
        var _didIteratorError = false;
        var _iteratorError = undefined;

        try {
          for (var _iterator = this.stops[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
            var stop = _step.value;
            grad.stop(stop[0], [stop[2]]);
          }
        } catch (err) {
          _didIteratorError = true;
          _iteratorError = err;
        } finally {
          try {
            if (!_iteratorNormalCompletion && _iterator.return != null) {
              _iterator.return();
            }
          } finally {
            if (_didIteratorError) {
              throw _iteratorError;
            }
          }
        }

        grad = grad.embed(this.matrix);
        var pageBBox = [0, 0, this.doc.page.width, this.doc.page.height];
        var form = this.doc.ref({
          Type: 'XObject',
          Subtype: 'Form',
          FormType: 1,
          BBox: pageBBox,
          Group: {
            Type: 'Group',
            S: 'Transparency',
            CS: 'DeviceGray'
          },
          Resources: {
            ProcSet: ['PDF', 'Text', 'ImageB', 'ImageC', 'ImageI'],
            Pattern: {
              Sh1: grad
            }
          }
        });
        form.write('/Pattern cs /Sh1 scn');
        form.end("".concat(pageBBox.join(' '), " re f"));
        var gstate = this.doc.ref({
          Type: 'ExtGState',
          SMask: {
            Type: 'Mask',
            S: 'Luminosity',
            G: form
          }
        });
        gstate.end();
        var opacityPattern = this.doc.ref({
          Type: 'Pattern',
          PatternType: 1,
          PaintType: 1,
          TilingType: 2,
          BBox: pageBBox,
          XStep: pageBBox[2],
          YStep: pageBBox[3],
          Resources: {
            ProcSet: ['PDF', 'Text', 'ImageB', 'ImageC', 'ImageI'],
            Pattern: {
              Sh1: pattern
            },
            ExtGState: {
              Gs1: gstate
            }
          }
        });
        opacityPattern.write('/Gs1 gs /Pattern cs /Sh1 scn');
        opacityPattern.end("".concat(pageBBox.join(' '), " re f"));
        this.doc.page.patterns[this.id] = opacityPattern;
      } else {
        this.doc.page.patterns[this.id] = pattern;
      }

      return pattern;
    }
  }, {
    key: "apply",
    value: function apply(op) {
      // apply gradient transform to existing document ctm
      var _this$doc$_ctm = _slicedToArray(this.doc._ctm, 6),
          m0 = _this$doc$_ctm[0],
          m1 = _this$doc$_ctm[1],
          m2 = _this$doc$_ctm[2],
          m3 = _this$doc$_ctm[3],
          m4 = _this$doc$_ctm[4],
          m5 = _this$doc$_ctm[5];

      var _this$transform = _slicedToArray(this.transform, 6),
          m11 = _this$transform[0],
          m12 = _this$transform[1],
          m21 = _this$transform[2],
          m22 = _this$transform[3],
          dx = _this$transform[4],
          dy = _this$transform[5];

      var m = [m0 * m11 + m2 * m12, m1 * m11 + m3 * m12, m0 * m21 + m2 * m22, m1 * m21 + m3 * m22, m0 * dx + m2 * dy + m4, m1 * dx + m3 * dy + m5];

      if (!this.embedded || m.join(' ') !== this.matrix.join(' ')) {
        this.embed(m);
      }

      return this.doc.addContent("/".concat(this.id, " ").concat(op));
    }
  }]);

  return PDFGradient;
}();

var PDFLinearGradient = /*#__PURE__*/function (_PDFGradient) {
  _inherits(PDFLinearGradient, _PDFGradient);

  function PDFLinearGradient(doc, x1, y1, x2, y2) {
    var _this;

    _classCallCheck(this, PDFLinearGradient);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(PDFLinearGradient).call(this, doc));
    _this.x1 = x1;
    _this.y1 = y1;
    _this.x2 = x2;
    _this.y2 = y2;
    return _this;
  }

  _createClass(PDFLinearGradient, [{
    key: "shader",
    value: function shader(fn) {
      return this.doc.ref({
        ShadingType: 2,
        ColorSpace: this._colorSpace,
        Coords: [this.x1, this.y1, this.x2, this.y2],
        Function: fn,
        Extend: [true, true]
      });
    }
  }, {
    key: "opacityGradient",
    value: function opacityGradient() {
      return new PDFLinearGradient(this.doc, this.x1, this.y1, this.x2, this.y2);
    }
  }]);

  return PDFLinearGradient;
}(PDFGradient);

var PDFRadialGradient = /*#__PURE__*/function (_PDFGradient2) {
  _inherits(PDFRadialGradient, _PDFGradient2);

  function PDFRadialGradient(doc, x1, y1, r1, x2, y2, r2) {
    var _this2;

    _classCallCheck(this, PDFRadialGradient);

    _this2 = _possibleConstructorReturn(this, _getPrototypeOf(PDFRadialGradient).call(this, doc));
    _this2.doc = doc;
    _this2.x1 = x1;
    _this2.y1 = y1;
    _this2.r1 = r1;
    _this2.x2 = x2;
    _this2.y2 = y2;
    _this2.r2 = r2;
    return _this2;
  }

  _createClass(PDFRadialGradient, [{
    key: "shader",
    value: function shader(fn) {
      return this.doc.ref({
        ShadingType: 3,
        ColorSpace: this._colorSpace,
        Coords: [this.x1, this.y1, this.r1, this.x2, this.y2, this.r2],
        Function: fn,
        Extend: [true, true]
      });
    }
  }, {
    key: "opacityGradient",
    value: function opacityGradient() {
      return new PDFRadialGradient(this.doc, this.x1, this.y1, this.r1, this.x2, this.y2, this.r2);
    }
  }]);

  return PDFRadialGradient;
}(PDFGradient);

var Gradient = {
  PDFGradient: PDFGradient,
  PDFLinearGradient: PDFLinearGradient,
  PDFRadialGradient: PDFRadialGradient
};
var PDFGradient$1 = Gradient.PDFGradient,
    PDFLinearGradient$1 = Gradient.PDFLinearGradient,
    PDFRadialGradient$1 = Gradient.PDFRadialGradient;
var ColorMixin = {
  initColor: function initColor() {
    // The opacity dictionaries
    this._opacityRegistry = {};
    this._opacityCount = 0;
    return this._gradCount = 0;
  },
  _normalizeColor: function _normalizeColor(color) {
    if (color instanceof PDFGradient$1) {
      return color;
    }

    if (typeof color === 'string') {
      if (color.charAt(0) === '#') {
        if (color.length === 4) {
          color = color.replace(/#([0-9A-F])([0-9A-F])([0-9A-F])/i, '#$1$1$2$2$3$3');
        }

        var hex = parseInt(color.slice(1), 16);
        color = [hex >> 16, hex >> 8 & 0xff, hex & 0xff];
      } else if (namedColors[color]) {
        color = namedColors[color];
      }
    }

    if (Array.isArray(color)) {
      // RGB
      if (color.length === 3) {
        color = color.map(function (part) {
          return part / 255;
        }); // CMYK
      } else if (color.length === 4) {
        color = color.map(function (part) {
          return part / 100;
        });
      }

      return color;
    }

    return null;
  },
  _setColor: function _setColor(color, stroke) {
    color = this._normalizeColor(color);

    if (!color) {
      return false;
    }

    var op = stroke ? 'SCN' : 'scn';

    if (color instanceof PDFGradient$1) {
      this._setColorSpace('Pattern', stroke);

      color.apply(op);
    } else {
      var space = color.length === 4 ? 'DeviceCMYK' : 'DeviceRGB';

      this._setColorSpace(space, stroke);

      color = color.join(' ');
      this.addContent("".concat(color, " ").concat(op));
    }

    return true;
  },
  _setColorSpace: function _setColorSpace(space, stroke) {
    var op = stroke ? 'CS' : 'cs';
    return this.addContent("/".concat(space, " ").concat(op));
  },
  fillColor: function fillColor(color, opacity) {
    var set = this._setColor(color, false);

    if (set) {
      this.fillOpacity(opacity);
    } // save this for text wrapper, which needs to reset
    // the fill color on new pages


    this._fillColor = [color, opacity];
    return this;
  },
  strokeColor: function strokeColor(color, opacity) {
    var set = this._setColor(color, true);

    if (set) {
      this.strokeOpacity(opacity);
    }

    return this;
  },
  opacity: function opacity(_opacity) {
    this._doOpacity(_opacity, _opacity);

    return this;
  },
  fillOpacity: function fillOpacity(opacity) {
    this._doOpacity(opacity, null);

    return this;
  },
  strokeOpacity: function strokeOpacity(opacity) {
    this._doOpacity(null, opacity);

    return this;
  },
  _doOpacity: function _doOpacity(fillOpacity, strokeOpacity) {
    var dictionary, name;

    if (fillOpacity == null && strokeOpacity == null) {
      return;
    }

    if (fillOpacity != null) {
      fillOpacity = Math.max(0, Math.min(1, fillOpacity));
    }

    if (strokeOpacity != null) {
      strokeOpacity = Math.max(0, Math.min(1, strokeOpacity));
    }

    var key = "".concat(fillOpacity, "_").concat(strokeOpacity);

    if (this._opacityRegistry[key]) {
      var _this$_opacityRegistr = _slicedToArray(this._opacityRegistry[key], 2);

      dictionary = _this$_opacityRegistr[0];
      name = _this$_opacityRegistr[1];
    } else {
      dictionary = {
        Type: 'ExtGState'
      };

      if (fillOpacity != null) {
        dictionary.ca = fillOpacity;
      }

      if (strokeOpacity != null) {
        dictionary.CA = strokeOpacity;
      }

      dictionary = this.ref(dictionary);
      dictionary.end();
      var id = ++this._opacityCount;
      name = "Gs".concat(id);
      this._opacityRegistry[key] = [dictionary, name];
    }

    this.page.ext_gstates[name] = dictionary;
    return this.addContent("/".concat(name, " gs"));
  },
  linearGradient: function linearGradient(x1, y1, x2, y2) {
    return new PDFLinearGradient$1(this, x1, y1, x2, y2);
  },
  radialGradient: function radialGradient(x1, y1, r1, x2, y2, r2) {
    return new PDFRadialGradient$1(this, x1, y1, r1, x2, y2, r2);
  }
};
var namedColors = {
  aliceblue: [240, 248, 255],
  antiquewhite: [250, 235, 215],
  aqua: [0, 255, 255],
  aquamarine: [127, 255, 212],
  azure: [240, 255, 255],
  beige: [245, 245, 220],
  bisque: [255, 228, 196],
  black: [0, 0, 0],
  blanchedalmond: [255, 235, 205],
  blue: [0, 0, 255],
  blueviolet: [138, 43, 226],
  brown: [165, 42, 42],
  burlywood: [222, 184, 135],
  cadetblue: [95, 158, 160],
  chartreuse: [127, 255, 0],
  chocolate: [210, 105, 30],
  coral: [255, 127, 80],
  cornflowerblue: [100, 149, 237],
  cornsilk: [255, 248, 220],
  crimson: [220, 20, 60],
  cyan: [0, 255, 255],
  darkblue: [0, 0, 139],
  darkcyan: [0, 139, 139],
  darkgoldenrod: [184, 134, 11],
  darkgray: [169, 169, 169],
  darkgreen: [0, 100, 0],
  darkgrey: [169, 169, 169],
  darkkhaki: [189, 183, 107],
  darkmagenta: [139, 0, 139],
  darkolivegreen: [85, 107, 47],
  darkorange: [255, 140, 0],
  darkorchid: [153, 50, 204],
  darkred: [139, 0, 0],
  darksalmon: [233, 150, 122],
  darkseagreen: [143, 188, 143],
  darkslateblue: [72, 61, 139],
  darkslategray: [47, 79, 79],
  darkslategrey: [47, 79, 79],
  darkturquoise: [0, 206, 209],
  darkviolet: [148, 0, 211],
  deeppink: [255, 20, 147],
  deepskyblue: [0, 191, 255],
  dimgray: [105, 105, 105],
  dimgrey: [105, 105, 105],
  dodgerblue: [30, 144, 255],
  firebrick: [178, 34, 34],
  floralwhite: [255, 250, 240],
  forestgreen: [34, 139, 34],
  fuchsia: [255, 0, 255],
  gainsboro: [220, 220, 220],
  ghostwhite: [248, 248, 255],
  gold: [255, 215, 0],
  goldenrod: [218, 165, 32],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  green: [0, 128, 0],
  greenyellow: [173, 255, 47],
  honeydew: [240, 255, 240],
  hotpink: [255, 105, 180],
  indianred: [205, 92, 92],
  indigo: [75, 0, 130],
  ivory: [255, 255, 240],
  khaki: [240, 230, 140],
  lavender: [230, 230, 250],
  lavenderblush: [255, 240, 245],
  lawngreen: [124, 252, 0],
  lemonchiffon: [255, 250, 205],
  lightblue: [173, 216, 230],
  lightcoral: [240, 128, 128],
  lightcyan: [224, 255, 255],
  lightgoldenrodyellow: [250, 250, 210],
  lightgray: [211, 211, 211],
  lightgreen: [144, 238, 144],
  lightgrey: [211, 211, 211],
  lightpink: [255, 182, 193],
  lightsalmon: [255, 160, 122],
  lightseagreen: [32, 178, 170],
  lightskyblue: [135, 206, 250],
  lightslategray: [119, 136, 153],
  lightslategrey: [119, 136, 153],
  lightsteelblue: [176, 196, 222],
  lightyellow: [255, 255, 224],
  lime: [0, 255, 0],
  limegreen: [50, 205, 50],
  linen: [250, 240, 230],
  magenta: [255, 0, 255],
  maroon: [128, 0, 0],
  mediumaquamarine: [102, 205, 170],
  mediumblue: [0, 0, 205],
  mediumorchid: [186, 85, 211],
  mediumpurple: [147, 112, 219],
  mediumseagreen: [60, 179, 113],
  mediumslateblue: [123, 104, 238],
  mediumspringgreen: [0, 250, 154],
  mediumturquoise: [72, 209, 204],
  mediumvioletred: [199, 21, 133],
  midnightblue: [25, 25, 112],
  mintcream: [245, 255, 250],
  mistyrose: [255, 228, 225],
  moccasin: [255, 228, 181],
  navajowhite: [255, 222, 173],
  navy: [0, 0, 128],
  oldlace: [253, 245, 230],
  olive: [128, 128, 0],
  olivedrab: [107, 142, 35],
  orange: [255, 165, 0],
  orangered: [255, 69, 0],
  orchid: [218, 112, 214],
  palegoldenrod: [238, 232, 170],
  palegreen: [152, 251, 152],
  paleturquoise: [175, 238, 238],
  palevioletred: [219, 112, 147],
  papayawhip: [255, 239, 213],
  peachpuff: [255, 218, 185],
  peru: [205, 133, 63],
  pink: [255, 192, 203],
  plum: [221, 160, 221],
  powderblue: [176, 224, 230],
  purple: [128, 0, 128],
  red: [255, 0, 0],
  rosybrown: [188, 143, 143],
  royalblue: [65, 105, 225],
  saddlebrown: [139, 69, 19],
  salmon: [250, 128, 114],
  sandybrown: [244, 164, 96],
  seagreen: [46, 139, 87],
  seashell: [255, 245, 238],
  sienna: [160, 82, 45],
  silver: [192, 192, 192],
  skyblue: [135, 206, 235],
  slateblue: [106, 90, 205],
  slategray: [112, 128, 144],
  slategrey: [112, 128, 144],
  snow: [255, 250, 250],
  springgreen: [0, 255, 127],
  steelblue: [70, 130, 180],
  tan: [210, 180, 140],
  teal: [0, 128, 128],
  thistle: [216, 191, 216],
  tomato: [255, 99, 71],
  turquoise: [64, 224, 208],
  violet: [238, 130, 238],
  wheat: [245, 222, 179],
  white: [255, 255, 255],
  whitesmoke: [245, 245, 245],
  yellow: [255, 255, 0],
  yellowgreen: [154, 205, 50]
};
var cx, cy, px, py, sx, sy;
cx = cy = px = py = sx = sy = 0;
var parameters = {
  A: 7,
  a: 7,
  C: 6,
  c: 6,
  H: 1,
  h: 1,
  L: 2,
  l: 2,
  M: 2,
  m: 2,
  Q: 4,
  q: 4,
  S: 4,
  s: 4,
  T: 2,
  t: 2,
  V: 1,
  v: 1,
  Z: 0,
  z: 0
};

var parse = function parse(path) {
  var cmd;
  var ret = [];
  var args = [];
  var curArg = '';
  var foundDecimal = false;
  var params = 0;
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = path[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var c = _step.value;

      if (parameters[c] != null) {
        params = parameters[c];

        if (cmd) {
          // save existing command
          if (curArg.length > 0) {
            args[args.length] = +curArg;
          }

          ret[ret.length] = {
            cmd: cmd,
            args: args
          };
          args = [];
          curArg = '';
          foundDecimal = false;
        }

        cmd = c;
      } else if ([' ', ','].includes(c) || c === '-' && curArg.length > 0 && curArg[curArg.length - 1] !== 'e' || c === '.' && foundDecimal) {
        if (curArg.length === 0) {
          continue;
        }

        if (args.length === params) {
          // handle reused commands
          ret[ret.length] = {
            cmd: cmd,
            args: args
          };
          args = [+curArg]; // handle assumed commands

          if (cmd === 'M') {
            cmd = 'L';
          }

          if (cmd === 'm') {
            cmd = 'l';
          }
        } else {
          args[args.length] = +curArg;
        }

        foundDecimal = c === '.'; // fix for negative numbers or repeated decimals with no delimeter between commands

        curArg = ['-', '.'].includes(c) ? c : '';
      } else {
        curArg += c;

        if (c === '.') {
          foundDecimal = true;
        }
      }
    } // add the last command

  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return != null) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  if (curArg.length > 0) {
    if (args.length === params) {
      // handle reused commands
      ret[ret.length] = {
        cmd: cmd,
        args: args
      };
      args = [+curArg]; // handle assumed commands

      if (cmd === 'M') {
        cmd = 'L';
      }

      if (cmd === 'm') {
        cmd = 'l';
      }
    } else {
      args[args.length] = +curArg;
    }
  }

  ret[ret.length] = {
    cmd: cmd,
    args: args
  };
  return ret;
};

var _apply = function apply(commands, doc) {
  // current point, control point, and subpath starting point
  cx = cy = px = py = sx = sy = 0; // run the commands

  for (var i = 0; i < commands.length; i++) {
    var c = commands[i];

    if (typeof runners[c.cmd] === 'function') {
      runners[c.cmd](doc, c.args);
    }
  }
};

var runners = {
  M: function M(doc, a) {
    cx = a[0];
    cy = a[1];
    px = py = null;
    sx = cx;
    sy = cy;
    return doc.moveTo(cx, cy);
  },
  m: function m(doc, a) {
    cx += a[0];
    cy += a[1];
    px = py = null;
    sx = cx;
    sy = cy;
    return doc.moveTo(cx, cy);
  },
  C: function C(doc, a) {
    cx = a[4];
    cy = a[5];
    px = a[2];
    py = a[3];
    return doc.bezierCurveTo.apply(doc, _toConsumableArray(a));
  },
  c: function c(doc, a) {
    doc.bezierCurveTo(a[0] + cx, a[1] + cy, a[2] + cx, a[3] + cy, a[4] + cx, a[5] + cy);
    px = cx + a[2];
    py = cy + a[3];
    cx += a[4];
    return cy += a[5];
  },
  S: function S(doc, a) {
    if (px === null) {
      px = cx;
      py = cy;
    }

    doc.bezierCurveTo(cx - (px - cx), cy - (py - cy), a[0], a[1], a[2], a[3]);
    px = a[0];
    py = a[1];
    cx = a[2];
    return cy = a[3];
  },
  s: function s(doc, a) {
    if (px === null) {
      px = cx;
      py = cy;
    }

    doc.bezierCurveTo(cx - (px - cx), cy - (py - cy), cx + a[0], cy + a[1], cx + a[2], cy + a[3]);
    px = cx + a[0];
    py = cy + a[1];
    cx += a[2];
    return cy += a[3];
  },
  Q: function Q(doc, a) {
    px = a[0];
    py = a[1];
    cx = a[2];
    cy = a[3];
    return doc.quadraticCurveTo(a[0], a[1], cx, cy);
  },
  q: function q(doc, a) {
    doc.quadraticCurveTo(a[0] + cx, a[1] + cy, a[2] + cx, a[3] + cy);
    px = cx + a[0];
    py = cy + a[1];
    cx += a[2];
    return cy += a[3];
  },
  T: function T(doc, a) {
    if (px === null) {
      px = cx;
      py = cy;
    } else {
      px = cx - (px - cx);
      py = cy - (py - cy);
    }

    doc.quadraticCurveTo(px, py, a[0], a[1]);
    px = cx - (px - cx);
    py = cy - (py - cy);
    cx = a[0];
    return cy = a[1];
  },
  t: function t(doc, a) {
    if (px === null) {
      px = cx;
      py = cy;
    } else {
      px = cx - (px - cx);
      py = cy - (py - cy);
    }

    doc.quadraticCurveTo(px, py, cx + a[0], cy + a[1]);
    cx += a[0];
    return cy += a[1];
  },
  A: function A(doc, a) {
    solveArc(doc, cx, cy, a);
    cx = a[5];
    return cy = a[6];
  },
  a: function a(doc, _a) {
    _a[5] += cx;
    _a[6] += cy;
    solveArc(doc, cx, cy, _a);
    cx = _a[5];
    return cy = _a[6];
  },
  L: function L(doc, a) {
    cx = a[0];
    cy = a[1];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  l: function l(doc, a) {
    cx += a[0];
    cy += a[1];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  H: function H(doc, a) {
    cx = a[0];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  h: function h(doc, a) {
    cx += a[0];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  V: function V(doc, a) {
    cy = a[0];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  v: function v(doc, a) {
    cy += a[0];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  Z: function Z(doc) {
    doc.closePath();
    cx = sx;
    return cy = sy;
  },
  z: function z(doc) {
    doc.closePath();
    cx = sx;
    return cy = sy;
  }
};

var solveArc = function solveArc(doc, x, y, coords) {
  var _coords = _slicedToArray(coords, 7),
      rx = _coords[0],
      ry = _coords[1],
      rot = _coords[2],
      large = _coords[3],
      sweep = _coords[4],
      ex = _coords[5],
      ey = _coords[6];

  var segs = arcToSegments(ex, ey, rx, ry, large, sweep, rot, x, y);
  var _iteratorNormalCompletion2 = true;
  var _didIteratorError2 = false;
  var _iteratorError2 = undefined;

  try {
    for (var _iterator2 = segs[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
      var seg = _step2.value;
      var bez = segmentToBezier.apply(void 0, _toConsumableArray(seg));
      doc.bezierCurveTo.apply(doc, _toConsumableArray(bez));
    }
  } catch (err) {
    _didIteratorError2 = true;
    _iteratorError2 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
        _iterator2.return();
      }
    } finally {
      if (_didIteratorError2) {
        throw _iteratorError2;
      }
    }
  }
}; // from Inkscape svgtopdf, thanks!


var arcToSegments = function arcToSegments(x, y, rx, ry, large, sweep, rotateX, ox, oy) {
  var th = rotateX * (Math.PI / 180);
  var sin_th = Math.sin(th);
  var cos_th = Math.cos(th);
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  px = cos_th * (ox - x) * 0.5 + sin_th * (oy - y) * 0.5;
  py = cos_th * (oy - y) * 0.5 - sin_th * (ox - x) * 0.5;
  var pl = px * px / (rx * rx) + py * py / (ry * ry);

  if (pl > 1) {
    pl = Math.sqrt(pl);
    rx *= pl;
    ry *= pl;
  }

  var a00 = cos_th / rx;
  var a01 = sin_th / rx;
  var a10 = -sin_th / ry;
  var a11 = cos_th / ry;
  var x0 = a00 * ox + a01 * oy;
  var y0 = a10 * ox + a11 * oy;
  var x1 = a00 * x + a01 * y;
  var y1 = a10 * x + a11 * y;
  var d = (x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0);
  var sfactor_sq = 1 / d - 0.25;

  if (sfactor_sq < 0) {
    sfactor_sq = 0;
  }

  var sfactor = Math.sqrt(sfactor_sq);

  if (sweep === large) {
    sfactor = -sfactor;
  }

  var xc = 0.5 * (x0 + x1) - sfactor * (y1 - y0);
  var yc = 0.5 * (y0 + y1) + sfactor * (x1 - x0);
  var th0 = Math.atan2(y0 - yc, x0 - xc);
  var th1 = Math.atan2(y1 - yc, x1 - xc);
  var th_arc = th1 - th0;

  if (th_arc < 0 && sweep === 1) {
    th_arc += 2 * Math.PI;
  } else if (th_arc > 0 && sweep === 0) {
    th_arc -= 2 * Math.PI;
  }

  var segments = Math.ceil(Math.abs(th_arc / (Math.PI * 0.5 + 0.001)));
  var result = [];

  for (var i = 0; i < segments; i++) {
    var th2 = th0 + i * th_arc / segments;
    var th3 = th0 + (i + 1) * th_arc / segments;
    result[i] = [xc, yc, th2, th3, rx, ry, sin_th, cos_th];
  }

  return result;
};

var segmentToBezier = function segmentToBezier(cx, cy, th0, th1, rx, ry, sin_th, cos_th) {
  var a00 = cos_th * rx;
  var a01 = -sin_th * ry;
  var a10 = sin_th * rx;
  var a11 = cos_th * ry;
  var th_half = 0.5 * (th1 - th0);
  var t = 8 / 3 * Math.sin(th_half * 0.5) * Math.sin(th_half * 0.5) / Math.sin(th_half);
  var x1 = cx + Math.cos(th0) - t * Math.sin(th0);
  var y1 = cy + Math.sin(th0) + t * Math.cos(th0);
  var x3 = cx + Math.cos(th1);
  var y3 = cy + Math.sin(th1);
  var x2 = x3 + t * Math.sin(th1);
  var y2 = y3 - t * Math.cos(th1);
  return [a00 * x1 + a01 * y1, a10 * x1 + a11 * y1, a00 * x2 + a01 * y2, a10 * x2 + a11 * y2, a00 * x3 + a01 * y3, a10 * x3 + a11 * y3];
};

var SVGPath = /*#__PURE__*/function () {
  function SVGPath() {
    _classCallCheck(this, SVGPath);
  }

  _createClass(SVGPath, null, [{
    key: "apply",
    value: function apply(doc, path) {
      var commands = parse(path);

      _apply(commands, doc);
    }
  }]);

  return SVGPath;
}();

var number$1 = PDFObject.number; // This constant is used to approximate a symmetrical arc using a cubic
// Bezier curve.

var KAPPA = 4.0 * ((Math.sqrt(2) - 1.0) / 3.0);
var VectorMixin = {
  initVector: function initVector() {
    this._ctm = [1, 0, 0, 1, 0, 0]; // current transformation matrix

    return this._ctmStack = [];
  },
  save: function save() {
    this._ctmStack.push(this._ctm.slice()); // TODO: save/restore colorspace and styles so not setting it unnessesarily all the time?


    return this.addContent('q');
  },
  restore: function restore() {
    this._ctm = this._ctmStack.pop() || [1, 0, 0, 1, 0, 0];
    return this.addContent('Q');
  },
  closePath: function closePath() {
    return this.addContent('h');
  },
  lineWidth: function lineWidth(w) {
    return this.addContent("".concat(number$1(w), " w"));
  },
  _CAP_STYLES: {
    BUTT: 0,
    ROUND: 1,
    SQUARE: 2
  },
  lineCap: function lineCap(c) {
    if (typeof c === 'string') {
      c = this._CAP_STYLES[c.toUpperCase()];
    }

    return this.addContent("".concat(c, " J"));
  },
  _JOIN_STYLES: {
    MITER: 0,
    ROUND: 1,
    BEVEL: 2
  },
  lineJoin: function lineJoin(j) {
    if (typeof j === 'string') {
      j = this._JOIN_STYLES[j.toUpperCase()];
    }

    return this.addContent("".concat(j, " j"));
  },
  miterLimit: function miterLimit(m) {
    return this.addContent("".concat(number$1(m), " M"));
  },
  dash: function dash(length) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var originalLength = length;

    if (!Array.isArray(length)) {
      length = [length, options.space || length];
    }

    var valid = length.every(function (x) {
      return Number.isFinite(x) && x > 0;
    });

    if (!valid) {
      throw new Error("dash(".concat(JSON.stringify(originalLength), ", ").concat(JSON.stringify(options), ") invalid, lengths must be numeric and greater than zero"));
    }

    length = length.map(number$1).join(' ');
    return this.addContent("[".concat(length, "] ").concat(number$1(options.phase || 0), " d"));
  },
  undash: function undash() {
    return this.addContent('[] 0 d');
  },
  moveTo: function moveTo(x, y) {
    return this.addContent("".concat(number$1(x), " ").concat(number$1(y), " m"));
  },
  lineTo: function lineTo(x, y) {
    return this.addContent("".concat(number$1(x), " ").concat(number$1(y), " l"));
  },
  bezierCurveTo: function bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y) {
    return this.addContent("".concat(number$1(cp1x), " ").concat(number$1(cp1y), " ").concat(number$1(cp2x), " ").concat(number$1(cp2y), " ").concat(number$1(x), " ").concat(number$1(y), " c"));
  },
  quadraticCurveTo: function quadraticCurveTo(cpx, cpy, x, y) {
    return this.addContent("".concat(number$1(cpx), " ").concat(number$1(cpy), " ").concat(number$1(x), " ").concat(number$1(y), " v"));
  },
  rect: function rect(x, y, w, h) {
    return this.addContent("".concat(number$1(x), " ").concat(number$1(y), " ").concat(number$1(w), " ").concat(number$1(h), " re"));
  },
  roundedRect: function roundedRect(x, y, w, h, r) {
    if (r == null) {
      r = 0;
    }

    r = Math.min(r, 0.5 * w, 0.5 * h); // amount to inset control points from corners (see `ellipse`)

    var c = r * (1.0 - KAPPA);
    this.moveTo(x + r, y);
    this.lineTo(x + w - r, y);
    this.bezierCurveTo(x + w - c, y, x + w, y + c, x + w, y + r);
    this.lineTo(x + w, y + h - r);
    this.bezierCurveTo(x + w, y + h - c, x + w - c, y + h, x + w - r, y + h);
    this.lineTo(x + r, y + h);
    this.bezierCurveTo(x + c, y + h, x, y + h - c, x, y + h - r);
    this.lineTo(x, y + r);
    this.bezierCurveTo(x, y + c, x + c, y, x + r, y);
    return this.closePath();
  },
  ellipse: function ellipse(x, y, r1, r2) {
    // based on http://stackoverflow.com/questions/2172798/how-to-draw-an-oval-in-html5-canvas/2173084#2173084
    if (r2 == null) {
      r2 = r1;
    }

    x -= r1;
    y -= r2;
    var ox = r1 * KAPPA;
    var oy = r2 * KAPPA;
    var xe = x + r1 * 2;
    var ye = y + r2 * 2;
    var xm = x + r1;
    var ym = y + r2;
    this.moveTo(x, ym);
    this.bezierCurveTo(x, ym - oy, xm - ox, y, xm, y);
    this.bezierCurveTo(xm + ox, y, xe, ym - oy, xe, ym);
    this.bezierCurveTo(xe, ym + oy, xm + ox, ye, xm, ye);
    this.bezierCurveTo(xm - ox, ye, x, ym + oy, x, ym);
    return this.closePath();
  },
  circle: function circle(x, y, radius) {
    return this.ellipse(x, y, radius);
  },
  arc: function arc(x, y, radius, startAngle, endAngle, anticlockwise) {
    if (anticlockwise == null) {
      anticlockwise = false;
    }

    var TWO_PI = 2.0 * Math.PI;
    var HALF_PI = 0.5 * Math.PI;
    var deltaAng = endAngle - startAngle;

    if (Math.abs(deltaAng) > TWO_PI) {
      // draw only full circle if more than that is specified
      deltaAng = TWO_PI;
    } else if (deltaAng !== 0 && anticlockwise !== deltaAng < 0) {
      // necessary to flip direction of rendering
      var dir = anticlockwise ? -1 : 1;
      deltaAng = dir * TWO_PI + deltaAng;
    }

    var numSegs = Math.ceil(Math.abs(deltaAng) / HALF_PI);
    var segAng = deltaAng / numSegs;
    var handleLen = segAng / HALF_PI * KAPPA * radius;
    var curAng = startAngle; // component distances between anchor point and control point

    var deltaCx = -Math.sin(curAng) * handleLen;
    var deltaCy = Math.cos(curAng) * handleLen; // anchor point

    var ax = x + Math.cos(curAng) * radius;
    var ay = y + Math.sin(curAng) * radius; // calculate and render segments

    this.moveTo(ax, ay);

    for (var segIdx = 0; segIdx < numSegs; segIdx++) {
      // starting control point
      var cp1x = ax + deltaCx;
      var cp1y = ay + deltaCy; // step angle

      curAng += segAng; // next anchor point

      ax = x + Math.cos(curAng) * radius;
      ay = y + Math.sin(curAng) * radius; // next control point delta

      deltaCx = -Math.sin(curAng) * handleLen;
      deltaCy = Math.cos(curAng) * handleLen; // ending control point

      var cp2x = ax - deltaCx;
      var cp2y = ay - deltaCy; // render segment

      this.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ax, ay);
    }

    return this;
  },
  polygon: function polygon() {
    for (var _len = arguments.length, points = new Array(_len), _key = 0; _key < _len; _key++) {
      points[_key] = arguments[_key];
    }

    this.moveTo.apply(this, _toConsumableArray(points.shift() || []));

    for (var _i = 0, _points = points; _i < _points.length; _i++) {
      var point = _points[_i];
      this.lineTo.apply(this, _toConsumableArray(point || []));
    }

    return this.closePath();
  },
  path: function path(_path) {
    SVGPath.apply(this, _path);
    return this;
  },
  _windingRule: function _windingRule(rule) {
    if (/even-?odd/.test(rule)) {
      return '*';
    }

    return '';
  },
  fill: function fill(color, rule) {
    if (/(even-?odd)|(non-?zero)/.test(color)) {
      rule = color;
      color = null;
    }

    if (color) {
      this.fillColor(color);
    }

    return this.addContent("f".concat(this._windingRule(rule)));
  },
  stroke: function stroke(color) {
    if (color) {
      this.strokeColor(color);
    }

    return this.addContent('S');
  },
  fillAndStroke: function fillAndStroke(fillColor, strokeColor, rule) {
    if (strokeColor == null) {
      strokeColor = fillColor;
    }

    var isFillRule = /(even-?odd)|(non-?zero)/;

    if (isFillRule.test(fillColor)) {
      rule = fillColor;
      fillColor = null;
    }

    if (isFillRule.test(strokeColor)) {
      rule = strokeColor;
      strokeColor = fillColor;
    }

    if (fillColor) {
      this.fillColor(fillColor);
      this.strokeColor(strokeColor);
    }

    return this.addContent("B".concat(this._windingRule(rule)));
  },
  clip: function clip(rule) {
    return this.addContent("W".concat(this._windingRule(rule), " n"));
  },
  transform: function transform(m11, m12, m21, m22, dx, dy) {
    // keep track of the current transformation matrix
    var m = this._ctm;

    var _m = _slicedToArray(m, 6),
        m0 = _m[0],
        m1 = _m[1],
        m2 = _m[2],
        m3 = _m[3],
        m4 = _m[4],
        m5 = _m[5];

    m[0] = m0 * m11 + m2 * m12;
    m[1] = m1 * m11 + m3 * m12;
    m[2] = m0 * m21 + m2 * m22;
    m[3] = m1 * m21 + m3 * m22;
    m[4] = m0 * dx + m2 * dy + m4;
    m[5] = m1 * dx + m3 * dy + m5;
    var values = [m11, m12, m21, m22, dx, dy].map(function (v) {
      return number$1(v);
    }).join(' ');
    return this.addContent("".concat(values, " cm"));
  },
  translate: function translate(x, y) {
    return this.transform(1, 0, 0, 1, x, y);
  },
  rotate: function rotate(angle) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var y;
    var rad = angle * Math.PI / 180;
    var cos = Math.cos(rad);
    var sin = Math.sin(rad);
    var x = y = 0;

    if (options.origin != null) {
      var _options$origin = _slicedToArray(options.origin, 2);

      x = _options$origin[0];
      y = _options$origin[1];
      var x1 = x * cos - y * sin;
      var y1 = x * sin + y * cos;
      x -= x1;
      y -= y1;
    }

    return this.transform(cos, sin, -sin, cos, x, y);
  },
  scale: function scale(xFactor, yFactor) {
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    var y;

    if (yFactor == null) {
      yFactor = xFactor;
    }

    if (typeof yFactor === 'object') {
      options = yFactor;
      yFactor = xFactor;
    }

    var x = y = 0;

    if (options.origin != null) {
      var _options$origin2 = _slicedToArray(options.origin, 2);

      x = _options$origin2[0];
      y = _options$origin2[1];
      x -= xFactor * x;
      y -= yFactor * y;
    }

    return this.transform(xFactor, 0, 0, yFactor, x, y);
  }
};
var WIN_ANSI_MAP = {
  402: 131,
  8211: 150,
  8212: 151,
  8216: 145,
  8217: 146,
  8218: 130,
  8220: 147,
  8221: 148,
  8222: 132,
  8224: 134,
  8225: 135,
  8226: 149,
  8230: 133,
  8364: 128,
  8240: 137,
  8249: 139,
  8250: 155,
  710: 136,
  8482: 153,
  338: 140,
  339: 156,
  732: 152,
  352: 138,
  353: 154,
  376: 159,
  381: 142,
  382: 158
};
var characters = ".notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n  \nspace         exclam         quotedbl       numbersign\ndollar        percent        ampersand      quotesingle\nparenleft     parenright     asterisk       plus\ncomma         hyphen         period         slash\nzero          one            two            three\nfour          five           six            seven\neight         nine           colon          semicolon\nless          equal          greater        question\n  \nat            A              B              C\nD             E              F              G\nH             I              J              K\nL             M              N              O\nP             Q              R              S\nT             U              V              W\nX             Y              Z              bracketleft\nbackslash     bracketright   asciicircum    underscore\n  \ngrave         a              b              c\nd             e              f              g\nh             i              j              k\nl             m              n              o\np             q              r              s\nt             u              v              w\nx             y              z              braceleft\nbar           braceright     asciitilde     .notdef\n  \nEuro          .notdef        quotesinglbase florin\nquotedblbase  ellipsis       dagger         daggerdbl\ncircumflex    perthousand    Scaron         guilsinglleft\nOE            .notdef        Zcaron         .notdef\n.notdef       quoteleft      quoteright     quotedblleft\nquotedblright bullet         endash         emdash\ntilde         trademark      scaron         guilsinglright\noe            .notdef        zcaron         ydieresis\n  \nspace         exclamdown     cent           sterling\ncurrency      yen            brokenbar      section\ndieresis      copyright      ordfeminine    guillemotleft\nlogicalnot    hyphen         registered     macron\ndegree        plusminus      twosuperior    threesuperior\nacute         mu             paragraph      periodcentered\ncedilla       onesuperior    ordmasculine   guillemotright\nonequarter    onehalf        threequarters  questiondown\n  \nAgrave        Aacute         Acircumflex    Atilde\nAdieresis     Aring          AE             Ccedilla\nEgrave        Eacute         Ecircumflex    Edieresis\nIgrave        Iacute         Icircumflex    Idieresis\nEth           Ntilde         Ograve         Oacute\nOcircumflex   Otilde         Odieresis      multiply\nOslash        Ugrave         Uacute         Ucircumflex\nUdieresis     Yacute         Thorn          germandbls\n  \nagrave        aacute         acircumflex    atilde\nadieresis     aring          ae             ccedilla\negrave        eacute         ecircumflex    edieresis\nigrave        iacute         icircumflex    idieresis\neth           ntilde         ograve         oacute\nocircumflex   otilde         odieresis      divide\noslash        ugrave         uacute         ucircumflex\nudieresis     yacute         thorn          ydieresis".split(/\s+/);

var AFMFont = /*#__PURE__*/function () {
  _createClass(AFMFont, null, [{
    key: "open",
    value: function open(filename) {
      return new AFMFont(fs.readFileSync(filename, 'utf8'));
    }
  }]);

  function AFMFont(contents) {
    _classCallCheck(this, AFMFont);

    this.contents = contents;
    this.attributes = {};
    this.glyphWidths = {};
    this.boundingBoxes = {};
    this.kernPairs = {};
    this.parse(); // todo: remove charWidths since appears to not be used

    this.charWidths = new Array(256);

    for (var char = 0; char <= 255; char++) {
      this.charWidths[char] = this.glyphWidths[characters[char]];
    }

    this.bbox = this.attributes['FontBBox'].split(/\s+/).map(function (e) {
      return +e;
    });
    this.ascender = +(this.attributes['Ascender'] || 0);
    this.descender = +(this.attributes['Descender'] || 0);
    this.xHeight = +(this.attributes['XHeight'] || 0);
    this.capHeight = +(this.attributes['CapHeight'] || 0);
    this.lineGap = this.bbox[3] - this.bbox[1] - (this.ascender - this.descender);
  }

  _createClass(AFMFont, [{
    key: "parse",
    value: function parse() {
      var section = '';
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = this.contents.split('\n')[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var line = _step.value;
          var match;
          var a;

          if (match = line.match(/^Start(\w+)/)) {
            section = match[1];
            continue;
          } else if (match = line.match(/^End(\w+)/)) {
            section = '';
            continue;
          }

          switch (section) {
            case 'FontMetrics':
              match = line.match(/(^\w+)\s+(.*)/);
              var key = match[1];
              var value = match[2];

              if (a = this.attributes[key]) {
                if (!Array.isArray(a)) {
                  a = this.attributes[key] = [a];
                }

                a.push(value);
              } else {
                this.attributes[key] = value;
              }

              break;

            case 'CharMetrics':
              if (!/^CH?\s/.test(line)) {
                continue;
              }

              var name = line.match(/\bN\s+(\.?\w+)\s*;/)[1];
              this.glyphWidths[name] = +line.match(/\bWX\s+(\d+)\s*;/)[1];
              break;

            case 'KernPairs':
              match = line.match(/^KPX\s+(\.?\w+)\s+(\.?\w+)\s+(-?\d+)/);

              if (match) {
                this.kernPairs[match[1] + '\0' + match[2]] = parseInt(match[3]);
              }

              break;
          }
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }
    }
  }, {
    key: "encodeText",
    value: function encodeText(text) {
      var res = [];

      for (var i = 0, len = text.length; i < len; i++) {
        var char = text.charCodeAt(i);
        char = WIN_ANSI_MAP[char] || char;
        res.push(char.toString(16));
      }

      return res;
    }
  }, {
    key: "glyphsForString",
    value: function glyphsForString(string) {
      var glyphs = [];

      for (var i = 0, len = string.length; i < len; i++) {
        var charCode = string.charCodeAt(i);
        glyphs.push(this.characterToGlyph(charCode));
      }

      return glyphs;
    }
  }, {
    key: "characterToGlyph",
    value: function characterToGlyph(character) {
      return characters[WIN_ANSI_MAP[character] || character] || '.notdef';
    }
  }, {
    key: "widthOfGlyph",
    value: function widthOfGlyph(glyph) {
      return this.glyphWidths[glyph] || 0;
    }
  }, {
    key: "getKernPair",
    value: function getKernPair(left, right) {
      return this.kernPairs[left + '\0' + right] || 0;
    }
  }, {
    key: "advancesForGlyphs",
    value: function advancesForGlyphs(glyphs) {
      var advances = [];

      for (var index = 0; index < glyphs.length; index++) {
        var left = glyphs[index];
        var right = glyphs[index + 1];
        advances.push(this.widthOfGlyph(left) + this.getKernPair(left, right));
      }

      return advances;
    }
  }]);

  return AFMFont;
}();

var PDFFont = /*#__PURE__*/function () {
  function PDFFont() {
    _classCallCheck(this, PDFFont);
  }

  _createClass(PDFFont, [{
    key: "encode",
    value: function encode() {
      throw new Error('Must be implemented by subclasses');
    }
  }, {
    key: "widthOfString",
    value: function widthOfString() {
      throw new Error('Must be implemented by subclasses');
    }
  }, {
    key: "ref",
    value: function ref() {
      return this.dictionary != null ? this.dictionary : this.dictionary = this.document.ref();
    }
  }, {
    key: "finalize",
    value: function finalize() {
      if (this.embedded || this.dictionary == null) {
        return;
      }

      this.embed();
      return this.embedded = true;
    }
  }, {
    key: "embed",
    value: function embed() {
      throw new Error('Must be implemented by subclasses');
    }
  }, {
    key: "lineHeight",
    value: function lineHeight(size, includeGap) {
      if (includeGap == null) {
        includeGap = false;
      }

      var gap = includeGap ? this.lineGap : 0;
      return (this.ascender + gap - this.descender) / 1000 * size;
    }
  }]);

  return PDFFont;
}();

var STANDARD_FONTS = {
  Courier: function Courier() {
    return fs.readFileSync(__dirname + '/data/Courier.afm', 'utf8');
  },
  'Courier-Bold': function CourierBold() {
    return fs.readFileSync(__dirname + '/data/Courier-Bold.afm', 'utf8');
  },
  'Courier-Oblique': function CourierOblique() {
    return fs.readFileSync(__dirname + '/data/Courier-Oblique.afm', 'utf8');
  },
  'Courier-BoldOblique': function CourierBoldOblique() {
    return fs.readFileSync(__dirname + '/data/Courier-BoldOblique.afm', 'utf8');
  },
  Helvetica: function Helvetica() {
    return fs.readFileSync(__dirname + '/data/Helvetica.afm', 'utf8');
  },
  'Helvetica-Bold': function HelveticaBold() {
    return fs.readFileSync(__dirname + '/data/Helvetica-Bold.afm', 'utf8');
  },
  'Helvetica-Oblique': function HelveticaOblique() {
    return fs.readFileSync(__dirname + '/data/Helvetica-Oblique.afm', 'utf8');
  },
  'Helvetica-BoldOblique': function HelveticaBoldOblique() {
    return fs.readFileSync(__dirname + '/data/Helvetica-BoldOblique.afm', 'utf8');
  },
  'Times-Roman': function TimesRoman() {
    return fs.readFileSync(__dirname + '/data/Times-Roman.afm', 'utf8');
  },
  'Times-Bold': function TimesBold() {
    return fs.readFileSync(__dirname + '/data/Times-Bold.afm', 'utf8');
  },
  'Times-Italic': function TimesItalic() {
    return fs.readFileSync(__dirname + '/data/Times-Italic.afm', 'utf8');
  },
  'Times-BoldItalic': function TimesBoldItalic() {
    return fs.readFileSync(__dirname + '/data/Times-BoldItalic.afm', 'utf8');
  },
  Symbol: function Symbol() {
    return fs.readFileSync(__dirname + '/data/Symbol.afm', 'utf8');
  },
  ZapfDingbats: function ZapfDingbats() {
    return fs.readFileSync(__dirname + '/data/ZapfDingbats.afm', 'utf8');
  }
};

var StandardFont = /*#__PURE__*/function (_PDFFont) {
  _inherits(StandardFont, _PDFFont);

  function StandardFont(document, name, id) {
    var _this;

    _classCallCheck(this, StandardFont);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(StandardFont).call(this));
    _this.document = document;
    _this.name = name;
    _this.id = id;
    _this.font = new AFMFont(STANDARD_FONTS[_this.name]());
    var _this$font = _this.font;
    _this.ascender = _this$font.ascender;
    _this.descender = _this$font.descender;
    _this.bbox = _this$font.bbox;
    _this.lineGap = _this$font.lineGap;
    _this.xHeight = _this$font.xHeight;
    _this.capHeight = _this$font.capHeight;
    return _this;
  }

  _createClass(StandardFont, [{
    key: "embed",
    value: function embed() {
      this.dictionary.data = {
        Type: 'Font',
        BaseFont: this.name,
        Subtype: 'Type1',
        Encoding: 'WinAnsiEncoding'
      };
      return this.dictionary.end();
    }
  }, {
    key: "encode",
    value: function encode(text) {
      var encoded = this.font.encodeText(text);
      var glyphs = this.font.glyphsForString("".concat(text));
      var advances = this.font.advancesForGlyphs(glyphs);
      var positions = [];

      for (var i = 0; i < glyphs.length; i++) {
        var glyph = glyphs[i];
        positions.push({
          xAdvance: advances[i],
          yAdvance: 0,
          xOffset: 0,
          yOffset: 0,
          advanceWidth: this.font.widthOfGlyph(glyph)
        });
      }

      return [encoded, positions];
    }
  }, {
    key: "widthOfString",
    value: function widthOfString(string, size) {
      var glyphs = this.font.glyphsForString("".concat(string));
      var advances = this.font.advancesForGlyphs(glyphs);
      var width = 0;
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = advances[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var advance = _step.value;
          width += advance;
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      var scale = size / 1000;
      return width * scale;
    }
  }], [{
    key: "isStandardFont",
    value: function isStandardFont(name) {
      return name in STANDARD_FONTS;
    }
  }]);

  return StandardFont;
}(PDFFont);

var toHex = function toHex(num) {
  return "0000".concat(num.toString(16)).slice(-4);
};

var EmbeddedFont = /*#__PURE__*/function (_PDFFont) {
  _inherits(EmbeddedFont, _PDFFont);

  function EmbeddedFont(document, font, id) {
    var _this;

    _classCallCheck(this, EmbeddedFont);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(EmbeddedFont).call(this));
    _this.document = document;
    _this.font = font;
    _this.id = id;
    _this.subset = _this.font.createSubset();
    _this.unicode = [[0]];
    _this.widths = [_this.font.getGlyph(0).advanceWidth];
    _this.name = _this.font.postscriptName;
    _this.scale = 1000 / _this.font.unitsPerEm;
    _this.ascender = _this.font.ascent * _this.scale;
    _this.descender = _this.font.descent * _this.scale;
    _this.xHeight = _this.font.xHeight * _this.scale;
    _this.capHeight = _this.font.capHeight * _this.scale;
    _this.lineGap = _this.font.lineGap * _this.scale;
    _this.bbox = _this.font.bbox;

    if (document.options.fontLayoutCache !== false) {
      _this.layoutCache = Object.create(null);
    }

    return _this;
  }

  _createClass(EmbeddedFont, [{
    key: "layoutRun",
    value: function layoutRun(text, features) {
      var run = this.font.layout(text, features); // Normalize position values

      for (var i = 0; i < run.positions.length; i++) {
        var position = run.positions[i];

        for (var key in position) {
          position[key] *= this.scale;
        }

        position.advanceWidth = run.glyphs[i].advanceWidth * this.scale;
      }

      return run;
    }
  }, {
    key: "layoutCached",
    value: function layoutCached(text) {
      if (!this.layoutCache) {
        return this.layoutRun(text);
      }

      var cached;

      if (cached = this.layoutCache[text]) {
        return cached;
      }

      var run = this.layoutRun(text);
      this.layoutCache[text] = run;
      return run;
    }
  }, {
    key: "layout",
    value: function layout(text, features, onlyWidth) {
      // Skip the cache if any user defined features are applied
      if (features) {
        return this.layoutRun(text, features);
      }

      var glyphs = onlyWidth ? null : [];
      var positions = onlyWidth ? null : [];
      var advanceWidth = 0; // Split the string by words to increase cache efficiency.
      // For this purpose, spaces and tabs are a good enough delimeter.

      var last = 0;
      var index = 0;

      while (index <= text.length) {
        var needle;

        if (index === text.length && last < index || (needle = text.charAt(index), [' ', '\t'].includes(needle))) {
          var run = this.layoutCached(text.slice(last, ++index));

          if (!onlyWidth) {
            glyphs = glyphs.concat(run.glyphs);
            positions = positions.concat(run.positions);
          }

          advanceWidth += run.advanceWidth;
          last = index;
        } else {
          index++;
        }
      }

      return {
        glyphs: glyphs,
        positions: positions,
        advanceWidth: advanceWidth
      };
    }
  }, {
    key: "encode",
    value: function encode(text, features) {
      var _this$layout = this.layout(text, features),
          glyphs = _this$layout.glyphs,
          positions = _this$layout.positions;

      var res = [];

      for (var i = 0; i < glyphs.length; i++) {
        var glyph = glyphs[i];
        var gid = this.subset.includeGlyph(glyph.id);
        res.push("0000".concat(gid.toString(16)).slice(-4));

        if (this.widths[gid] == null) {
          this.widths[gid] = glyph.advanceWidth * this.scale;
        }

        if (this.unicode[gid] == null) {
          this.unicode[gid] = glyph.codePoints;
        }
      }

      return [res, positions];
    }
  }, {
    key: "widthOfString",
    value: function widthOfString(string, size, features) {
      var width = this.layout(string, features, true).advanceWidth;
      var scale = size / 1000;
      return width * scale;
    }
  }, {
    key: "embed",
    value: function embed() {
      var _this2 = this;

      var isCFF = this.subset.cff != null;
      var fontFile = this.document.ref();

      if (isCFF) {
        fontFile.data.Subtype = 'CIDFontType0C';
      }

      this.subset.encodeStream().on('data', function (data) {
        return fontFile.write(data);
      }).on('end', function () {
        return fontFile.end();
      });
      var familyClass = ((this.font['OS/2'] != null ? this.font['OS/2'].sFamilyClass : undefined) || 0) >> 8;
      var flags = 0;

      if (this.font.post.isFixedPitch) {
        flags |= 1 << 0;
      }

      if (1 <= familyClass && familyClass <= 7) {
        flags |= 1 << 1;
      }

      flags |= 1 << 2; // assume the font uses non-latin characters

      if (familyClass === 10) {
        flags |= 1 << 3;
      }

      if (this.font.head.macStyle.italic) {
        flags |= 1 << 6;
      } // generate a tag (6 uppercase letters. 16 is the char code offset from '1' to 'A'. 74 will map to 'Z')


      var tag = [1, 2, 3, 4, 5, 6].map(function (i) {
        return String.fromCharCode((_this2.id.charCodeAt(i) || 74) + 16);
      }).join('');
      var name = tag + '+' + this.font.postscriptName;
      var bbox = this.font.bbox;
      var descriptor = this.document.ref({
        Type: 'FontDescriptor',
        FontName: name,
        Flags: flags,
        FontBBox: [bbox.minX * this.scale, bbox.minY * this.scale, bbox.maxX * this.scale, bbox.maxY * this.scale],
        ItalicAngle: this.font.italicAngle,
        Ascent: this.ascender,
        Descent: this.descender,
        CapHeight: (this.font.capHeight || this.font.ascent) * this.scale,
        XHeight: (this.font.xHeight || 0) * this.scale,
        StemV: 0
      }); // not sure how to calculate this

      if (isCFF) {
        descriptor.data.FontFile3 = fontFile;
      } else {
        descriptor.data.FontFile2 = fontFile;
      }

      descriptor.end();
      var descendantFontData = {
        Type: 'Font',
        Subtype: 'CIDFontType0',
        BaseFont: name,
        CIDSystemInfo: {
          Registry: new String('Adobe'),
          Ordering: new String('Identity'),
          Supplement: 0
        },
        FontDescriptor: descriptor,
        W: [0, this.widths]
      };

      if (!isCFF) {
        descendantFontData.Subtype = 'CIDFontType2';
        descendantFontData.CIDToGIDMap = 'Identity';
      }

      var descendantFont = this.document.ref(descendantFontData);
      descendantFont.end();
      this.dictionary.data = {
        Type: 'Font',
        Subtype: 'Type0',
        BaseFont: name,
        Encoding: 'Identity-H',
        DescendantFonts: [descendantFont],
        ToUnicode: this.toUnicodeCmap()
      };
      return this.dictionary.end();
    } // Maps the glyph ids encoded in the PDF back to unicode strings
    // Because of ligature substitutions and the like, there may be one or more
    // unicode characters represented by each glyph.

  }, {
    key: "toUnicodeCmap",
    value: function toUnicodeCmap() {
      var cmap = this.document.ref();
      var entries = [];
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = this.unicode[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var codePoints = _step.value;
          var encoded = []; // encode codePoints to utf16

          var _iteratorNormalCompletion2 = true;
          var _didIteratorError2 = false;
          var _iteratorError2 = undefined;

          try {
            for (var _iterator2 = codePoints[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
              var value = _step2.value;

              if (value > 0xffff) {
                value -= 0x10000;
                encoded.push(toHex(value >>> 10 & 0x3ff | 0xd800));
                value = 0xdc00 | value & 0x3ff;
              }

              encoded.push(toHex(value));
            }
          } catch (err) {
            _didIteratorError2 = true;
            _iteratorError2 = err;
          } finally {
            try {
              if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
                _iterator2.return();
              }
            } finally {
              if (_didIteratorError2) {
                throw _iteratorError2;
              }
            }
          }

          entries.push("<".concat(encoded.join(' '), ">"));
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      cmap.end("/CIDInit /ProcSet findresource begin\n12 dict begin\nbegincmap\n/CIDSystemInfo <<\n  /Registry (Adobe)\n  /Ordering (UCS)\n  /Supplement 0\n>> def\n/CMapName /Adobe-Identity-UCS def\n/CMapType 2 def\n1 begincodespacerange\n<0000><ffff>\nendcodespacerange\n1 beginbfrange\n<0000> <".concat(toHex(entries.length - 1), "> [").concat(entries.join(' '), "]\nendbfrange\nendcmap\nCMapName currentdict /CMap defineresource pop\nend\nend"));
      return cmap;
    }
  }]);

  return EmbeddedFont;
}(PDFFont);

var PDFFontFactory = /*#__PURE__*/function () {
  function PDFFontFactory() {
    _classCallCheck(this, PDFFontFactory);
  }

  _createClass(PDFFontFactory, null, [{
    key: "open",
    value: function open(document, src, family, id) {
      var font;

      if (typeof src === 'string') {
        if (StandardFont.isStandardFont(src)) {
          return new StandardFont(document, src, id);
        }

        src = fs.readFileSync(src);
      }

      if (Buffer.isBuffer(src)) {
        font = _fontkit.default.create(src, family);
      } else if (src instanceof Uint8Array) {
        font = _fontkit.default.create(new Buffer(src), family);
      } else if (src instanceof ArrayBuffer) {
        font = _fontkit.default.create(new Buffer(new Uint8Array(src)), family);
      }

      if (font == null) {
        throw new Error('Not a supported font format or standard PDF font.');
      }

      return new EmbeddedFont(document, font, id);
    }
  }]);

  return PDFFontFactory;
}();

var FontsMixin = {
  initFonts: function initFonts() {
    var defaultFont = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'Helvetica'; // Lookup table for embedded fonts

    this._fontFamilies = {};
    this._fontCount = 0; // Font state

    this._fontSize = 12;
    this._font = null;
    this._registeredFonts = {}; // Set the default font

    if (defaultFont) {
      this.font(defaultFont);
    }
  },
  font: function font(src, family, size) {
    var cacheKey, font;

    if (typeof family === 'number') {
      size = family;
      family = null;
    } // check registered fonts if src is a string


    if (typeof src === 'string' && this._registeredFonts[src]) {
      cacheKey = src;
      var _this$_registeredFont = this._registeredFonts[src];
      src = _this$_registeredFont.src;
      family = _this$_registeredFont.family;
    } else {
      cacheKey = family || src;

      if (typeof cacheKey !== 'string') {
        cacheKey = null;
      }
    }

    if (size != null) {
      this.fontSize(size);
    } // fast path: check if the font is already in the PDF


    if (font = this._fontFamilies[cacheKey]) {
      this._font = font;
      return this;
    } // load the font


    var id = "F".concat(++this._fontCount);
    this._font = PDFFontFactory.open(this, src, family, id); // check for existing font familes with the same name already in the PDF
    // useful if the font was passed as a buffer

    if (font = this._fontFamilies[this._font.name]) {
      this._font = font;
      return this;
    } // save the font for reuse later


    if (cacheKey) {
      this._fontFamilies[cacheKey] = this._font;
    }

    if (this._font.name) {
      this._fontFamilies[this._font.name] = this._font;
    }

    return this;
  },
  fontSize: function fontSize(_fontSize) {
    this._fontSize = _fontSize;
    return this;
  },
  currentLineHeight: function currentLineHeight(includeGap) {
    if (includeGap == null) {
      includeGap = false;
    }

    return this._font.lineHeight(this._fontSize, includeGap);
  },
  registerFont: function registerFont(name, src, family) {
    this._registeredFonts[name] = {
      src: src,
      family: family
    };
    return this;
  }
};

var LineWrapper = /*#__PURE__*/function (_EventEmitter) {
  _inherits(LineWrapper, _EventEmitter);

  function LineWrapper(document, options) {
    var _this;

    _classCallCheck(this, LineWrapper);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(LineWrapper).call(this));
    _this.document = document;
    _this.indent = options.indent || 0;
    _this.characterSpacing = options.characterSpacing || 0;
    _this.wordSpacing = options.wordSpacing === 0;
    _this.columns = options.columns || 1;
    _this.columnGap = options.columnGap != null ? options.columnGap : 18; // 1/4 inch

    _this.lineWidth = (options.width - _this.columnGap * (_this.columns - 1)) / _this.columns;
    _this.spaceLeft = _this.lineWidth;
    _this.startX = _this.document.x;
    _this.startY = _this.document.y;
    _this.column = 1;
    _this.ellipsis = options.ellipsis;
    _this.continuedX = 0;
    _this.features = options.features; // calculate the maximum Y position the text can appear at

    if (options.height != null) {
      _this.height = options.height;
      _this.maxY = _this.startY + options.height;
    } else {
      _this.maxY = _this.document.page.maxY();
    } // handle paragraph indents


    _this.on('firstLine', function (options) {
      // if this is the first line of the text segment, and
      // we're continuing where we left off, indent that much
      // otherwise use the user specified indent option
      var indent = _this.continuedX || _this.indent;
      _this.document.x += indent;
      _this.lineWidth -= indent;
      return _this.once('line', function () {
        _this.document.x -= indent;
        _this.lineWidth += indent;

        if (options.continued && !_this.continuedX) {
          _this.continuedX = _this.indent;
        }

        if (!options.continued) {
          return _this.continuedX = 0;
        }
      });
    }); // handle left aligning last lines of paragraphs


    _this.on('lastLine', function (options) {
      var align = options.align;

      if (align === 'justify') {
        options.align = 'left';
      }

      _this.lastLine = true;
      return _this.once('line', function () {
        _this.document.y += options.paragraphGap || 0;
        options.align = align;
        return _this.lastLine = false;
      });
    });

    return _this;
  }

  _createClass(LineWrapper, [{
    key: "wordWidth",
    value: function wordWidth(word) {
      return this.document.widthOfString(word, this) + this.characterSpacing + this.wordSpacing;
    }
  }, {
    key: "eachWord",
    value: function eachWord(text, fn) {
      // setup a unicode line breaker
      var bk;
      var breaker = new _linebreak.default(text);
      var last = null;
      var wordWidths = Object.create(null);

      while (bk = breaker.nextBreak()) {
        var shouldContinue;
        var word = text.slice((last != null ? last.position : undefined) || 0, bk.position);
        var w = wordWidths[word] != null ? wordWidths[word] : wordWidths[word] = this.wordWidth(word); // if the word is longer than the whole line, chop it up
        // TODO: break by grapheme clusters, not JS string characters

        if (w > this.lineWidth + this.continuedX) {
          // make some fake break objects
          var lbk = last;
          var fbk = {};

          while (word.length) {
            // fit as much of the word as possible into the space we have
            var l, mightGrow;

            if (w > this.spaceLeft) {
              // start our check at the end of our available space - this method is faster than a loop of each character and it resolves
              // an issue with long loops when processing massive words, such as a huge number of spaces
              l = Math.ceil(this.spaceLeft / (w / word.length));
              w = this.wordWidth(word.slice(0, l));
              mightGrow = w <= this.spaceLeft && l < word.length;
            } else {
              l = word.length;
            }

            var mustShrink = w > this.spaceLeft && l > 0; // shrink or grow word as necessary after our near-guess above

            while (mustShrink || mightGrow) {
              if (mustShrink) {
                w = this.wordWidth(word.slice(0, --l));
                mustShrink = w > this.spaceLeft && l > 0;
              } else {
                w = this.wordWidth(word.slice(0, ++l));
                mustShrink = w > this.spaceLeft && l > 0;
                mightGrow = w <= this.spaceLeft && l < word.length;
              }
            } // check for the edge case where a single character cannot fit into a line.


            if (l === 0 && this.spaceLeft === this.lineWidth) {
              l = 1;
            } // send a required break unless this is the last piece and a linebreak is not specified


            fbk.required = bk.required || l < word.length;
            shouldContinue = fn(word.slice(0, l), w, fbk, lbk);
            lbk = {
              required: false
            }; // get the remaining piece of the word

            word = word.slice(l);
            w = this.wordWidth(word);

            if (shouldContinue === false) {
              break;
            }
          }
        } else {
          // otherwise just emit the break as it was given to us
          shouldContinue = fn(word, w, bk, last);
        }

        if (shouldContinue === false) {
          break;
        }

        last = bk;
      }
    }
  }, {
    key: "wrap",
    value: function wrap(text, options) {
      var _this2 = this; // override options from previous continued fragments


      if (options.indent != null) {
        this.indent = options.indent;
      }

      if (options.characterSpacing != null) {
        this.characterSpacing = options.characterSpacing;
      }

      if (options.wordSpacing != null) {
        this.wordSpacing = options.wordSpacing;
      }

      if (options.ellipsis != null) {
        this.ellipsis = options.ellipsis;
      } // make sure we're actually on the page
      // and that the first line of is never by
      // itself at the bottom of a page (orphans)


      var nextY = this.document.y + this.document.currentLineHeight(true);

      if (this.document.y > this.maxY || nextY > this.maxY) {
        this.nextSection();
      }

      var buffer = '';
      var textWidth = 0;
      var wc = 0;
      var lc = 0;
      var y = this.document.y; // used to reset Y pos if options.continued (below)

      var emitLine = function emitLine() {
        options.textWidth = textWidth + _this2.wordSpacing * (wc - 1);
        options.wordCount = wc;
        options.lineWidth = _this2.lineWidth;
        y = _this2.document.y;

        _this2.emit('line', buffer, options, _this2);

        return lc++;
      };

      this.emit('sectionStart', options, this);
      this.eachWord(text, function (word, w, bk, last) {
        if (last == null || last.required) {
          _this2.emit('firstLine', options, _this2);

          _this2.spaceLeft = _this2.lineWidth;
        }

        if (w <= _this2.spaceLeft) {
          buffer += word;
          textWidth += w;
          wc++;
        }

        if (bk.required || w > _this2.spaceLeft) {
          // if the user specified a max height and an ellipsis, and is about to pass the
          // max height and max columns after the next line, append the ellipsis
          var lh = _this2.document.currentLineHeight(true);

          if (_this2.height != null && _this2.ellipsis && _this2.document.y + lh * 2 > _this2.maxY && _this2.column >= _this2.columns) {
            if (_this2.ellipsis === true) {
              _this2.ellipsis = '…';
            } // map default ellipsis character


            buffer = buffer.replace(/\s+$/, '');
            textWidth = _this2.wordWidth(buffer + _this2.ellipsis); // remove characters from the buffer until the ellipsis fits
            // to avoid inifinite loop need to stop while-loop if buffer is empty string

            while (buffer && textWidth > _this2.lineWidth) {
              buffer = buffer.slice(0, -1).replace(/\s+$/, '');
              textWidth = _this2.wordWidth(buffer + _this2.ellipsis);
            } // need to add ellipsis only if there is enough space for it


            if (textWidth <= _this2.lineWidth) {
              buffer = buffer + _this2.ellipsis;
            }

            textWidth = _this2.wordWidth(buffer);
          }

          if (bk.required) {
            if (w > _this2.spaceLeft) {
              emitLine();
              buffer = word;
              textWidth = w;
              wc = 1;
            }

            _this2.emit('lastLine', options, _this2);
          }

          emitLine(); // if we've reached the edge of the page,
          // continue on a new page or column

          if (_this2.document.y + lh > _this2.maxY) {
            var shouldContinue = _this2.nextSection(); // stop if we reached the maximum height


            if (!shouldContinue) {
              wc = 0;
              buffer = '';
              return false;
            }
          } // reset the space left and buffer


          if (bk.required) {
            _this2.spaceLeft = _this2.lineWidth;
            buffer = '';
            textWidth = 0;
            return wc = 0;
          } else {
            // reset the space left and buffer
            _this2.spaceLeft = _this2.lineWidth - w;
            buffer = word;
            textWidth = w;
            return wc = 1;
          }
        } else {
          return _this2.spaceLeft -= w;
        }
      });

      if (wc > 0) {
        this.emit('lastLine', options, this);
        emitLine();
      }

      this.emit('sectionEnd', options, this); // if the wrap is set to be continued, save the X position
      // to start the first line of the next segment at, and reset
      // the y position

      if (options.continued === true) {
        if (lc > 1) {
          this.continuedX = 0;
        }

        this.continuedX += options.textWidth || 0;
        return this.document.y = y;
      } else {
        return this.document.x = this.startX;
      }
    }
  }, {
    key: "nextSection",
    value: function nextSection(options) {
      this.emit('sectionEnd', options, this);

      if (++this.column > this.columns) {
        // if a max height was specified by the user, we're done.
        // otherwise, the default is to make a new page at the bottom.
        if (this.height != null) {
          return false;
        }

        this.document.addPage();
        this.column = 1;
        this.startY = this.document.page.margins.top;
        this.maxY = this.document.page.maxY();
        this.document.x = this.startX;

        if (this.document._fillColor) {
          var _this$document;

          (_this$document = this.document).fillColor.apply(_this$document, _toConsumableArray(this.document._fillColor));
        }

        this.emit('pageBreak', options, this);
      } else {
        this.document.x += this.lineWidth + this.columnGap;
        this.document.y = this.startY;
        this.emit('columnBreak', options, this);
      }

      this.emit('sectionStart', options, this);
      return true;
    }
  }]);

  return LineWrapper;
}(_events.EventEmitter);

var number$2 = PDFObject.number;
var TextMixin = {
  initText: function initText() {
    this._line = this._line.bind(this); // Current coordinates

    this.x = 0;
    this.y = 0;
    return this._lineGap = 0;
  },
  lineGap: function lineGap(_lineGap) {
    this._lineGap = _lineGap;
    return this;
  },
  moveDown: function moveDown(lines) {
    if (lines == null) {
      lines = 1;
    }

    this.y += this.currentLineHeight(true) * lines + this._lineGap;
    return this;
  },
  moveUp: function moveUp(lines) {
    if (lines == null) {
      lines = 1;
    }

    this.y -= this.currentLineHeight(true) * lines + this._lineGap;
    return this;
  },
  _text: function _text(text, x, y, options, lineCallback) {
    options = this._initOptions(x, y, options); // Convert text to a string

    text = text == null ? '' : "".concat(text); // if the wordSpacing option is specified, remove multiple consecutive spaces

    if (options.wordSpacing) {
      text = text.replace(/\s{2,}/g, ' ');
    } // word wrapping


    if (options.width) {
      var wrapper = this._wrapper;

      if (!wrapper) {
        wrapper = new LineWrapper(this, options);
        wrapper.on('line', lineCallback);
      }

      this._wrapper = options.continued ? wrapper : null;
      this._textOptions = options.continued ? options : null;
      wrapper.wrap(text, options); // render paragraphs as single lines
    } else {
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = text.split('\n')[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var line = _step.value;
          lineCallback(line, options);
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }
    }

    return this;
  },
  text: function text(_text2, x, y, options) {
    return this._text(_text2, x, y, options, this._line);
  },
  widthOfString: function widthOfString(string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return this._font.widthOfString(string, this._fontSize, options.features) + (options.characterSpacing || 0) * (string.length - 1);
  },
  heightOfString: function heightOfString(text, options) {
    var _this = this;

    var x = this.x,
        y = this.y;
    options = this._initOptions(options);
    options.height = Infinity; // don't break pages

    var lineGap = options.lineGap || this._lineGap || 0;

    this._text(text, this.x, this.y, options, function () {
      return _this.y += _this.currentLineHeight(true) + lineGap;
    });

    var height = this.y - y;
    this.x = x;
    this.y = y;
    return height;
  },
  list: function list(_list, x, y, options, wrapper) {
    var _this2 = this;

    options = this._initOptions(x, y, options);
    var listType = options.listType || 'bullet';
    var unit = Math.round(this._font.ascender / 1000 * this._fontSize);
    var midLine = unit / 2;
    var r = options.bulletRadius || unit / 3;
    var indent = options.textIndent || (listType === 'bullet' ? r * 5 : unit * 2);
    var itemIndent = options.bulletIndent || (listType === 'bullet' ? r * 8 : unit * 2);
    var level = 1;
    var items = [];
    var levels = [];
    var numbers = [];

    var flatten = function flatten(list) {
      var n = 1;

      for (var _i = 0; _i < list.length; _i++) {
        var item = list[_i];

        if (Array.isArray(item)) {
          level++;
          flatten(item);
          level--;
        } else {
          items.push(item);
          levels.push(level);

          if (listType !== 'bullet') {
            numbers.push(n++);
          }
        }
      }
    };

    flatten(_list);

    var label = function label(n) {
      switch (listType) {
        case 'numbered':
          return "".concat(n, ".");

        case 'lettered':
          var letter = String.fromCharCode((n - 1) % 26 + 65);
          var times = Math.floor((n - 1) / 26 + 1);
          var text = Array(times + 1).join(letter);
          return "".concat(text, ".");
      }
    };

    wrapper = new LineWrapper(this, options);
    wrapper.on('line', this._line);
    level = 1;
    var i = 0;
    wrapper.on('firstLine', function () {
      var l;

      if ((l = levels[i++]) !== level) {
        var diff = itemIndent * (l - level);
        _this2.x += diff;
        wrapper.lineWidth -= diff;
        level = l;
      }

      switch (listType) {
        case 'bullet':
          _this2.circle(_this2.x - indent + r, _this2.y + midLine, r);

          return _this2.fill();

        case 'numbered':
        case 'lettered':
          var text = label(numbers[i - 1]);
          return _this2._fragment(text, _this2.x - indent, _this2.y, options);
      }
    });
    wrapper.on('sectionStart', function () {
      var pos = indent + itemIndent * (level - 1);
      _this2.x += pos;
      return wrapper.lineWidth -= pos;
    });
    wrapper.on('sectionEnd', function () {
      var pos = indent + itemIndent * (level - 1);
      _this2.x -= pos;
      return wrapper.lineWidth += pos;
    });
    wrapper.wrap(items.join('\n'), options);
    return this;
  },
  _initOptions: function _initOptions() {
    var x = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var y = arguments.length > 1 ? arguments[1] : undefined;
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    if (typeof x === 'object') {
      options = x;
      x = null;
    } // clone options object


    var result = Object.assign({}, options); // extend options with previous values for continued text

    if (this._textOptions) {
      for (var key in this._textOptions) {
        var val = this._textOptions[key];

        if (key !== 'continued') {
          if (result[key] === undefined) {
            result[key] = val;
          }
        }
      }
    } // Update the current position


    if (x != null) {
      this.x = x;
    }

    if (y != null) {
      this.y = y;
    } // wrap to margins if no x or y position passed


    if (result.lineBreak !== false) {
      if (result.width == null) {
        result.width = this.page.width - this.x - this.page.margins.right;
      }

      result.width = Math.max(result.width, 0);
    }

    if (!result.columns) {
      result.columns = 0;
    }

    if (result.columnGap == null) {
      result.columnGap = 18;
    } // 1/4 inch


    return result;
  },
  _line: function _line(text) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var wrapper = arguments.length > 2 ? arguments[2] : undefined;

    this._fragment(text, this.x, this.y, options);

    var lineGap = options.lineGap || this._lineGap || 0;

    if (!wrapper) {
      return this.x += this.widthOfString(text);
    } else {
      return this.y += this.currentLineHeight(true) + lineGap;
    }
  },
  _fragment: function _fragment(text, x, y, options) {
    var _this3 = this;

    var dy, encoded, i, positions, textWidth, words;
    text = "".concat(text).replace(/\n/g, '');

    if (text.length === 0) {
      return;
    } // handle options


    var align = options.align || 'left';
    var wordSpacing = options.wordSpacing || 0;
    var characterSpacing = options.characterSpacing || 0; // text alignments

    if (options.width) {
      switch (align) {
        case 'right':
          textWidth = this.widthOfString(text.replace(/\s+$/, ''), options);
          x += options.lineWidth - textWidth;
          break;

        case 'center':
          x += options.lineWidth / 2 - options.textWidth / 2;
          break;

        case 'justify':
          // calculate the word spacing value
          words = text.trim().split(/\s+/);
          textWidth = this.widthOfString(text.replace(/\s+/g, ''), options);
          var spaceWidth = this.widthOfString(' ') + characterSpacing;
          wordSpacing = Math.max(0, (options.lineWidth - textWidth) / Math.max(1, words.length - 1) - spaceWidth);
          break;
      }
    } // text baseline alignments based on http://wiki.apache.org/xmlgraphics-fop/LineLayout/AlignmentHandling


    if (typeof options.baseline === 'number') {
      dy = -options.baseline;
    } else {
      switch (options.baseline) {
        case 'svg-middle':
          dy = 0.5 * this._font.xHeight;
          break;

        case 'middle':
        case 'svg-central':
          dy = 0.5 * (this._font.descender + this._font.ascender);
          break;

        case 'bottom':
        case 'ideographic':
          dy = this._font.descender;
          break;

        case 'alphabetic':
          dy = 0;
          break;

        case 'mathematical':
          dy = 0.5 * this._font.ascender;
          break;

        case 'hanging':
          dy = 0.8 * this._font.ascender;
          break;

        case 'top':
          dy = this._font.ascender;
          break;

        default:
          dy = this._font.ascender;
      }

      dy = dy / 1000 * this._fontSize;
    } // calculate the actual rendered width of the string after word and character spacing


    var renderedWidth = options.textWidth + wordSpacing * (options.wordCount - 1) + characterSpacing * (text.length - 1); // create link annotations if the link option is given

    if (options.link != null) {
      this.link(x, y, renderedWidth, this.currentLineHeight(), options.link);
    }

    if (options.goTo != null) {
      this.goTo(x, y, renderedWidth, this.currentLineHeight(), options.goTo);
    }

    if (options.destination != null) {
      this.addNamedDestination(options.destination, 'XYZ', x, y, null);
    } // create underline or strikethrough line


    if (options.underline || options.strike) {
      this.save();

      if (!options.stroke) {
        this.strokeColor.apply(this, _toConsumableArray(this._fillColor || []));
      }

      var lineWidth = this._fontSize < 10 ? 0.5 : Math.floor(this._fontSize / 10);
      this.lineWidth(lineWidth);
      var d = options.underline ? 1 : 2;
      var lineY = y + this.currentLineHeight() / d;

      if (options.underline) {
        lineY -= lineWidth;
      }

      this.moveTo(x, lineY);
      this.lineTo(x + renderedWidth, lineY);
      this.stroke();
      this.restore();
    }

    this.save(); // oblique (angle in degrees or boolean)

    if (options.oblique) {
      var skew;

      if (typeof options.oblique === 'number') {
        skew = -Math.tan(options.oblique * Math.PI / 180);
      } else {
        skew = -0.25;
      }

      this.transform(1, 0, 0, 1, x, y);
      this.transform(1, 0, skew, 1, -skew * dy, 0);
      this.transform(1, 0, 0, 1, -x, -y);
    } // flip coordinate system


    this.transform(1, 0, 0, -1, 0, this.page.height);
    y = this.page.height - y - dy; // add current font to page if necessary

    if (this.page.fonts[this._font.id] == null) {
      this.page.fonts[this._font.id] = this._font.ref();
    } // begin the text object


    this.addContent('BT'); // text position

    this.addContent("1 0 0 1 ".concat(number$2(x), " ").concat(number$2(y), " Tm")); // font and font size

    this.addContent("/".concat(this._font.id, " ").concat(number$2(this._fontSize), " Tf")); // rendering mode

    var mode = options.fill && options.stroke ? 2 : options.stroke ? 1 : 0;

    if (mode) {
      this.addContent("".concat(mode, " Tr"));
    } // Character spacing


    if (characterSpacing) {
      this.addContent("".concat(number$2(characterSpacing), " Tc"));
    } // Add the actual text
    // If we have a word spacing value, we need to encode each word separately
    // since the normal Tw operator only works on character code 32, which isn't
    // used for embedded fonts.


    if (wordSpacing) {
      words = text.trim().split(/\s+/);
      wordSpacing += this.widthOfString(' ') + characterSpacing;
      wordSpacing *= 1000 / this._fontSize;
      encoded = [];
      positions = [];
      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;

      try {
        for (var _iterator2 = words[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          var word = _step2.value;

          var _this$_font$encode = this._font.encode(word, options.features),
              _this$_font$encode2 = _slicedToArray(_this$_font$encode, 2),
              encodedWord = _this$_font$encode2[0],
              positionsWord = _this$_font$encode2[1];

          encoded = encoded.concat(encodedWord);
          positions = positions.concat(positionsWord); // add the word spacing to the end of the word
          // clone object because of cache

          var space = {};
          var object = positions[positions.length - 1];

          for (var key in object) {
            var val = object[key];
            space[key] = val;
          }

          space.xAdvance += wordSpacing;
          positions[positions.length - 1] = space;
        }
      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
            _iterator2.return();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }
    } else {
      var _this$_font$encode3 = this._font.encode(text, options.features);

      var _this$_font$encode4 = _slicedToArray(_this$_font$encode3, 2);

      encoded = _this$_font$encode4[0];
      positions = _this$_font$encode4[1];
    }

    var scale = this._fontSize / 1000;
    var commands = [];
    var last = 0;
    var hadOffset = false; // Adds a segment of text to the TJ command buffer

    var addSegment = function addSegment(cur) {
      if (last < cur) {
        var hex = encoded.slice(last, cur).join('');
        var advance = positions[cur - 1].xAdvance - positions[cur - 1].advanceWidth;
        commands.push("<".concat(hex, "> ").concat(number$2(-advance)));
      }

      return last = cur;
    }; // Flushes the current TJ commands to the output stream


    var flush = function flush(i) {
      addSegment(i);

      if (commands.length > 0) {
        _this3.addContent("[".concat(commands.join(' '), "] TJ"));

        return commands.length = 0;
      }
    };

    for (i = 0; i < positions.length; i++) {
      // If we have an x or y offset, we have to break out of the current TJ command
      // so we can move the text position.
      var pos = positions[i];

      if (pos.xOffset || pos.yOffset) {
        // Flush the current buffer
        flush(i); // Move the text position and flush just the current character

        this.addContent("1 0 0 1 ".concat(number$2(x + pos.xOffset * scale), " ").concat(number$2(y + pos.yOffset * scale), " Tm"));
        flush(i + 1);
        hadOffset = true;
      } else {
        // If the last character had an offset, reset the text position
        if (hadOffset) {
          this.addContent("1 0 0 1 ".concat(number$2(x), " ").concat(number$2(y), " Tm"));
          hadOffset = false;
        } // Group segments that don't have any advance adjustments


        if (pos.xAdvance - pos.advanceWidth !== 0) {
          addSegment(i + 1);
        }
      }

      x += pos.xAdvance * scale;
    } // Flush any remaining commands


    flush(i); // end the text object

    this.addContent('ET'); // restore flipped coordinate system

    return this.restore();
  }
};
var MARKERS = [0xffc0, 0xffc1, 0xffc2, 0xffc3, 0xffc5, 0xffc6, 0xffc7, 0xffc8, 0xffc9, 0xffca, 0xffcb, 0xffcc, 0xffcd, 0xffce, 0xffcf];
var COLOR_SPACE_MAP = {
  1: 'DeviceGray',
  3: 'DeviceRGB',
  4: 'DeviceCMYK'
};

var JPEG = /*#__PURE__*/function () {
  function JPEG(data, label) {
    _classCallCheck(this, JPEG);

    var marker;
    this.data = data;
    this.label = label;

    if (this.data.readUInt16BE(0) !== 0xffd8) {
      throw 'SOI not found in JPEG';
    }

    var pos = 2;

    while (pos < this.data.length) {
      marker = this.data.readUInt16BE(pos);
      pos += 2;

      if (MARKERS.includes(marker)) {
        break;
      }

      pos += this.data.readUInt16BE(pos);
    }

    if (!MARKERS.includes(marker)) {
      throw 'Invalid JPEG.';
    }

    pos += 2;
    this.bits = this.data[pos++];
    this.height = this.data.readUInt16BE(pos);
    pos += 2;
    this.width = this.data.readUInt16BE(pos);
    pos += 2;
    var channels = this.data[pos++];
    this.colorSpace = COLOR_SPACE_MAP[channels];
    this.obj = null;
  }

  _createClass(JPEG, [{
    key: "embed",
    value: function embed(document) {
      if (this.obj) {
        return;
      }

      this.obj = document.ref({
        Type: 'XObject',
        Subtype: 'Image',
        BitsPerComponent: this.bits,
        Width: this.width,
        Height: this.height,
        ColorSpace: this.colorSpace,
        Filter: 'DCTDecode'
      }); // add extra decode params for CMYK images. By swapping the
      // min and max values from the default, we invert the colors. See
      // section 4.8.4 of the spec.

      if (this.colorSpace === 'DeviceCMYK') {
        this.obj.data['Decode'] = [1.0, 0.0, 1.0, 0.0, 1.0, 0.0, 1.0, 0.0];
      }

      this.obj.end(this.data); // free memory

      return this.data = null;
    }
  }]);

  return JPEG;
}();

var PNGImage = /*#__PURE__*/function () {
  function PNGImage(data, label) {
    _classCallCheck(this, PNGImage);

    this.label = label;
    this.image = new _pngJs.default(data);
    this.width = this.image.width;
    this.height = this.image.height;
    this.imgData = this.image.imgData;
    this.obj = null;
  }

  _createClass(PNGImage, [{
    key: "embed",
    value: function embed(document) {
      var dataDecoded = false;
      this.document = document;

      if (this.obj) {
        return;
      }

      var hasAlphaChannel = this.image.hasAlphaChannel;
      var isInterlaced = this.image.interlaceMethod === 1;
      this.obj = this.document.ref({
        Type: 'XObject',
        Subtype: 'Image',
        BitsPerComponent: hasAlphaChannel ? 8 : this.image.bits,
        Width: this.width,
        Height: this.height,
        Filter: 'FlateDecode'
      });

      if (!hasAlphaChannel) {
        var params = this.document.ref({
          Predictor: isInterlaced ? 1 : 15,
          Colors: this.image.colors,
          BitsPerComponent: this.image.bits,
          Columns: this.width
        });
        this.obj.data['DecodeParms'] = params;
        params.end();
      }

      if (this.image.palette.length === 0) {
        this.obj.data['ColorSpace'] = this.image.colorSpace;
      } else {
        // embed the color palette in the PDF as an object stream
        var palette = this.document.ref();
        palette.end(new Buffer(this.image.palette)); // build the color space array for the image

        this.obj.data['ColorSpace'] = ['Indexed', 'DeviceRGB', this.image.palette.length / 3 - 1, palette];
      } // For PNG color types 0, 2 and 3, the transparency data is stored in
      // a dedicated PNG chunk.


      if (this.image.transparency.grayscale != null) {
        // Use Color Key Masking (spec section 4.8.5)
        // An array with N elements, where N is two times the number of color components.
        var val = this.image.transparency.grayscale;
        this.obj.data['Mask'] = [val, val];
      } else if (this.image.transparency.rgb) {
        // Use Color Key Masking (spec section 4.8.5)
        // An array with N elements, where N is two times the number of color components.
        var rgb = this.image.transparency.rgb;
        var mask = [];
        var _iteratorNormalCompletion = true;
        var _didIteratorError = false;
        var _iteratorError = undefined;

        try {
          for (var _iterator = rgb[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
            var x = _step.value;
            mask.push(x, x);
          }
        } catch (err) {
          _didIteratorError = true;
          _iteratorError = err;
        } finally {
          try {
            if (!_iteratorNormalCompletion && _iterator.return != null) {
              _iterator.return();
            }
          } finally {
            if (_didIteratorError) {
              throw _iteratorError;
            }
          }
        }

        this.obj.data['Mask'] = mask;
      } else if (this.image.transparency.indexed) {
        // Create a transparency SMask for the image based on the data
        // in the PLTE and tRNS sections. See below for details on SMasks.
        dataDecoded = true;
        return this.loadIndexedAlphaChannel();
      } else if (hasAlphaChannel) {
        // For PNG color types 4 and 6, the transparency data is stored as a alpha
        // channel mixed in with the main image data. Separate this data out into an
        // SMask object and store it separately in the PDF.
        dataDecoded = true;
        return this.splitAlphaChannel();
      }

      if (isInterlaced && !dataDecoded) {
        return this.decodeData();
      }

      this.finalize();
    }
  }, {
    key: "finalize",
    value: function finalize() {
      if (this.alphaChannel) {
        var sMask = this.document.ref({
          Type: 'XObject',
          Subtype: 'Image',
          Height: this.height,
          Width: this.width,
          BitsPerComponent: 8,
          Filter: 'FlateDecode',
          ColorSpace: 'DeviceGray',
          Decode: [0, 1]
        });
        sMask.end(this.alphaChannel);
        this.obj.data['SMask'] = sMask;
      } // add the actual image data


      this.obj.end(this.imgData); // free memory

      this.image = null;
      return this.imgData = null;
    }
  }, {
    key: "splitAlphaChannel",
    value: function splitAlphaChannel() {
      var _this = this;

      return this.image.decodePixels(function (pixels) {
        var a, p;
        var colorCount = _this.image.colors;
        var pixelCount = _this.width * _this.height;
        var imgData = new Buffer(pixelCount * colorCount);
        var alphaChannel = new Buffer(pixelCount);
        var i = p = a = 0;
        var len = pixels.length; // For 16bit images copy only most significant byte (MSB) - PNG data is always stored in network byte order (MSB first)

        var skipByteCount = _this.image.bits === 16 ? 1 : 0;

        while (i < len) {
          for (var colorIndex = 0; colorIndex < colorCount; colorIndex++) {
            imgData[p++] = pixels[i++];
            i += skipByteCount;
          }

          alphaChannel[a++] = pixels[i++];
          i += skipByteCount;
        }

        _this.imgData = _zlib.default.deflateSync(imgData);
        _this.alphaChannel = _zlib.default.deflateSync(alphaChannel);
        return _this.finalize();
      });
    }
  }, {
    key: "loadIndexedAlphaChannel",
    value: function loadIndexedAlphaChannel() {
      var _this2 = this;

      var transparency = this.image.transparency.indexed;
      return this.image.decodePixels(function (pixels) {
        var alphaChannel = new Buffer(_this2.width * _this2.height);
        var i = 0;

        for (var j = 0, end = pixels.length; j < end; j++) {
          alphaChannel[i++] = transparency[pixels[j]];
        }

        _this2.alphaChannel = _zlib.default.deflateSync(alphaChannel);
        return _this2.finalize();
      });
    }
  }, {
    key: "decodeData",
    value: function decodeData() {
      var _this3 = this;

      this.image.decodePixels(function (pixels) {
        _this3.imgData = _zlib.default.deflateSync(pixels);

        _this3.finalize();
      });
    }
  }]);

  return PNGImage;
}();

var PDFImage = /*#__PURE__*/function () {
  function PDFImage() {
    _classCallCheck(this, PDFImage);
  }

  _createClass(PDFImage, null, [{
    key: "open",
    value: function open(src, label) {
      var data;

      if (Buffer.isBuffer(src)) {
        data = src;
      } else if (src instanceof ArrayBuffer) {
        data = new Buffer(new Uint8Array(src));
      } else {
        var match;

        if (match = /^data:.+;base64,(.*)$/.exec(src)) {
          data = new Buffer(match[1], 'base64');
        } else {
          data = fs.readFileSync(src);

          if (!data) {
            return;
          }
        }
      }

      if (data[0] === 0xff && data[1] === 0xd8) {
        return new JPEG(data, label);
      } else if (data[0] === 0x89 && data.toString('ascii', 1, 4) === 'PNG') {
        return new PNGImage(data, label);
      } else {
        throw new Error('Unknown image format.');
      }
    }
  }]);

  return PDFImage;
}();

var ImagesMixin = {
  initImages: function initImages() {
    this._imageRegistry = {};
    return this._imageCount = 0;
  },
  image: function image(src, x, y) {
    var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    var bh, bp, bw, image, ip, left, left1;

    if (typeof x === 'object') {
      options = x;
      x = null;
    }

    x = (left = x != null ? x : options.x) != null ? left : this.x;
    y = (left1 = y != null ? y : options.y) != null ? left1 : this.y;

    if (typeof src === 'string') {
      image = this._imageRegistry[src];
    }

    if (!image) {
      if (src.width && src.height) {
        image = src;
      } else {
        image = this.openImage(src);
      }
    }

    if (!image.obj) {
      image.embed(this);
    }

    if (this.page.xobjects[image.label] == null) {
      this.page.xobjects[image.label] = image.obj;
    }

    var w = options.width || image.width;
    var h = options.height || image.height;

    if (options.width && !options.height) {
      var wp = w / image.width;
      w = image.width * wp;
      h = image.height * wp;
    } else if (options.height && !options.width) {
      var hp = h / image.height;
      w = image.width * hp;
      h = image.height * hp;
    } else if (options.scale) {
      w = image.width * options.scale;
      h = image.height * options.scale;
    } else if (options.fit) {
      var _options$fit = _slicedToArray(options.fit, 2);

      bw = _options$fit[0];
      bh = _options$fit[1];
      bp = bw / bh;
      ip = image.width / image.height;

      if (ip > bp) {
        w = bw;
        h = bw / ip;
      } else {
        h = bh;
        w = bh * ip;
      }
    } else if (options.cover) {
      var _options$cover = _slicedToArray(options.cover, 2);

      bw = _options$cover[0];
      bh = _options$cover[1];
      bp = bw / bh;
      ip = image.width / image.height;

      if (ip > bp) {
        h = bh;
        w = bh * ip;
      } else {
        w = bw;
        h = bw / ip;
      }
    }

    if (options.fit || options.cover) {
      if (options.align === 'center') {
        x = x + bw / 2 - w / 2;
      } else if (options.align === 'right') {
        x = x + bw - w;
      }

      if (options.valign === 'center') {
        y = y + bh / 2 - h / 2;
      } else if (options.valign === 'bottom') {
        y = y + bh - h;
      }
    } // create link annotations if the link option is given


    if (options.link != null) {
      this.link(x, y, w, h, options.link);
    }

    if (options.goTo != null) {
      this.goTo(x, y, w, h, options.goTo);
    }

    if (options.destination != null) {
      this.addNamedDestination(options.destination, 'XYZ', x, y, null);
    } // Set the current y position to below the image if it is in the document flow


    if (this.y === y) {
      this.y += h;
    }

    this.save();
    this.transform(w, 0, 0, -h, x, y + h);
    this.addContent("/".concat(image.label, " Do"));
    this.restore();
    return this;
  },
  openImage: function openImage(src) {
    var image;

    if (typeof src === 'string') {
      image = this._imageRegistry[src];
    }

    if (!image) {
      image = PDFImage.open(src, "I".concat(++this._imageCount));

      if (typeof src === 'string') {
        this._imageRegistry[src] = image;
      }
    }

    return image;
  }
};
var AnnotationsMixin = {
  annotate: function annotate(x, y, w, h, options) {
    options.Type = 'Annot';
    options.Rect = this._convertRect(x, y, w, h);
    options.Border = [0, 0, 0];

    if (options.Subtype === 'Link' && typeof options.F === 'undefined') {
      options.F = 1 << 2; // Print Annotation Flag
    }

    if (options.Subtype !== 'Link') {
      if (options.C == null) {
        options.C = this._normalizeColor(options.color || [0, 0, 0]);
      }
    } // convert colors


    delete options.color;

    if (typeof options.Dest === 'string') {
      options.Dest = new String(options.Dest);
    } // Capitalize keys


    for (var key in options) {
      var val = options[key];
      options[key[0].toUpperCase() + key.slice(1)] = val;
    }

    var ref = this.ref(options);
    this.page.annotations.push(ref);
    ref.end();
    return this;
  },
  note: function note(x, y, w, h, contents) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    options.Subtype = 'Text';
    options.Contents = new String(contents);
    options.Name = 'Comment';

    if (options.color == null) {
      options.color = [243, 223, 92];
    }

    return this.annotate(x, y, w, h, options);
  },
  goTo: function goTo(x, y, w, h, name) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    options.Subtype = 'Link';
    options.A = this.ref({
      S: 'GoTo',
      D: new String(name)
    });
    options.A.end();
    return this.annotate(x, y, w, h, options);
  },
  link: function link(x, y, w, h, url) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    options.Subtype = 'Link';

    if (typeof url === 'number') {
      // Link to a page in the document (the page must already exist)
      var pages = this._root.data.Pages.data;

      if (url >= 0 && url < pages.Kids.length) {
        options.A = this.ref({
          S: 'GoTo',
          D: [pages.Kids[url], 'XYZ', null, null, null]
        });
        options.A.end();
      } else {
        throw new Error("The document has no page ".concat(url));
      }
    } else {
      // Link to an external url
      options.A = this.ref({
        S: 'URI',
        URI: new String(url)
      });
      options.A.end();
    }

    return this.annotate(x, y, w, h, options);
  },
  _markup: function _markup(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};

    var _this$_convertRect = this._convertRect(x, y, w, h),
        _this$_convertRect2 = _slicedToArray(_this$_convertRect, 4),
        x1 = _this$_convertRect2[0],
        y1 = _this$_convertRect2[1],
        x2 = _this$_convertRect2[2],
        y2 = _this$_convertRect2[3];

    options.QuadPoints = [x1, y2, x2, y2, x1, y1, x2, y1];
    options.Contents = new String();
    return this.annotate(x, y, w, h, options);
  },
  highlight: function highlight(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'Highlight';

    if (options.color == null) {
      options.color = [241, 238, 148];
    }

    return this._markup(x, y, w, h, options);
  },
  underline: function underline(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'Underline';
    return this._markup(x, y, w, h, options);
  },
  strike: function strike(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'StrikeOut';
    return this._markup(x, y, w, h, options);
  },
  lineAnnotation: function lineAnnotation(x1, y1, x2, y2) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'Line';
    options.Contents = new String();
    options.L = [x1, this.page.height - y1, x2, this.page.height - y2];
    return this.annotate(x1, y1, x2, y2, options);
  },
  rectAnnotation: function rectAnnotation(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'Square';
    options.Contents = new String();
    return this.annotate(x, y, w, h, options);
  },
  ellipseAnnotation: function ellipseAnnotation(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'Circle';
    options.Contents = new String();
    return this.annotate(x, y, w, h, options);
  },
  textAnnotation: function textAnnotation(x, y, w, h, text) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    options.Subtype = 'FreeText';
    options.Contents = new String(text);
    options.DA = new String();
    return this.annotate(x, y, w, h, options);
  },
  _convertRect: function _convertRect(x1, y1, w, h) {
    // flip y1 and y2
    var y2 = y1;
    y1 += h; // make x2

    var x2 = x1 + w; // apply current transformation matrix to points

    var _this$_ctm = _slicedToArray(this._ctm, 6),
        m0 = _this$_ctm[0],
        m1 = _this$_ctm[1],
        m2 = _this$_ctm[2],
        m3 = _this$_ctm[3],
        m4 = _this$_ctm[4],
        m5 = _this$_ctm[5];

    x1 = m0 * x1 + m2 * y1 + m4;
    y1 = m1 * x1 + m3 * y1 + m5;
    x2 = m0 * x2 + m2 * y2 + m4;
    y2 = m1 * x2 + m3 * y2 + m5;
    return [x1, y1, x2, y2];
  }
};

var PDFOutline = /*#__PURE__*/function () {
  function PDFOutline(document, parent, title, dest) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {
      expanded: false
    };

    _classCallCheck(this, PDFOutline);

    this.document = document;
    this.options = options;
    this.outlineData = {};

    if (dest !== null) {
      this.outlineData['Dest'] = [dest.dictionary, 'Fit'];
    }

    if (parent !== null) {
      this.outlineData['Parent'] = parent;
    }

    if (title !== null) {
      this.outlineData['Title'] = new String(title);
    }

    this.dictionary = this.document.ref(this.outlineData);
    this.children = [];
  }

  _createClass(PDFOutline, [{
    key: "addItem",
    value: function addItem(title) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
        expanded: false
      };
      var result = new PDFOutline(this.document, this.dictionary, title, this.document.page, options);
      this.children.push(result);
      return result;
    }
  }, {
    key: "endOutline",
    value: function endOutline() {
      if (this.children.length > 0) {
        if (this.options.expanded) {
          this.outlineData.Count = this.children.length;
        }

        var first = this.children[0],
            last = this.children[this.children.length - 1];
        this.outlineData.First = first.dictionary;
        this.outlineData.Last = last.dictionary;

        for (var i = 0, len = this.children.length; i < len; i++) {
          var child = this.children[i];

          if (i > 0) {
            child.outlineData.Prev = this.children[i - 1].dictionary;
          }

          if (i < this.children.length - 1) {
            child.outlineData.Next = this.children[i + 1].dictionary;
          }

          child.endOutline();
        }
      }

      return this.dictionary.end();
    }
  }]);

  return PDFOutline;
}();

var OutlineMixin = {
  initOutline: function initOutline() {
    return this.outline = new PDFOutline(this, null, null, null);
  },
  endOutline: function endOutline() {
    this.outline.endOutline();

    if (this.outline.children.length > 0) {
      this._root.data.Outlines = this.outline.dictionary;
      return this._root.data.PageMode = 'UseOutlines';
    }
  }
};
var FIELD_FLAGS = {
  readOnly: 1,
  required: 2,
  noExport: 4,
  multiline: 0x1000,
  password: 0x2000,
  toggleToOffButton: 0x4000,
  radioButton: 0x8000,
  pushButton: 0x10000,
  combo: 0x20000,
  edit: 0x40000,
  sort: 0x80000,
  multiSelect: 0x200000,
  noSpell: 0x400000
};
var FIELD_JUSTIFY = {
  left: 0,
  center: 1,
  right: 2
};
var VALUE_MAP = {
  value: 'V',
  defaultValue: 'DV'
};
var FORMAT_SPECIAL = {
  zip: '0',
  zipPlus4: '1',
  zip4: '1',
  phone: '2',
  ssn: '3'
};
var FORMAT_DEFAULT = {
  number: {
    nDec: 0,
    sepComma: false,
    negStyle: 'MinusBlack',
    currency: '',
    currencyPrepend: true
  },
  percent: {
    nDec: 0,
    sepComma: false
  }
};
var AcroFormMixin = {
  /**
   * Must call if adding AcroForms to a document. Must also call font() before
   * this method to set the default font.
   */
  initForm: function initForm() {
    if (!this._font) {
      throw new Error('Must set a font before calling initForm method');
    }

    this._acroform = {
      fonts: {},
      defaultFont: this._font.name
    };
    this._acroform.fonts[this._font.id] = this._font.ref();
    var data = {
      Fields: [],
      NeedAppearances: true,
      DA: new String("/".concat(this._font.id, " 0 Tf 0 g")),
      DR: {
        Font: {}
      }
    };
    data.DR.Font[this._font.id] = this._font.ref();
    var AcroForm = this.ref(data);
    this._root.data.AcroForm = AcroForm;
    return this;
  },

  /**
   * Called automatically by document.js
   */
  endAcroForm: function endAcroForm() {
    var _this = this;

    if (this._root.data.AcroForm) {
      if (!Object.keys(this._acroform.fonts).length && !this._acroform.defaultFont) {
        throw new Error('No fonts specified for PDF form');
      }

      var fontDict = this._root.data.AcroForm.data.DR.Font;
      Object.keys(this._acroform.fonts).forEach(function (name) {
        fontDict[name] = _this._acroform.fonts[name];
      });

      this._root.data.AcroForm.data.Fields.forEach(function (fieldRef) {
        _this._endChild(fieldRef);
      });

      this._root.data.AcroForm.end();
    }

    return this;
  },
  _endChild: function _endChild(ref) {
    var _this2 = this;

    if (Array.isArray(ref.data.Kids)) {
      ref.data.Kids.forEach(function (childRef) {
        _this2._endChild(childRef);
      });
      ref.end();
    }

    return this;
  },

  /**
   * Creates and adds a form field to the document. Form fields are intermediate
   * nodes in a PDF form that are used to specify form name heirarchy and form
   * value defaults.
   * @param {string} name - field name (T attribute in field dictionary)
   * @param {object} options  - other attributes to include in field dictionary
   */
  formField: function formField(name) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    var fieldDict = this._fieldDict(name, null, options);

    var fieldRef = this.ref(fieldDict);

    this._addToParent(fieldRef);

    return fieldRef;
  },

  /**
   * Creates and adds a Form Annotation to the document. Form annotations are
   * called Widget annotations internally within a PDF file.
   * @param {string} name - form field name (T attribute of widget annotation
   * dictionary)
   * @param {number} x
   * @param {number} y
   * @param {number} w
   * @param {number} h
   * @param {object} options
   */
  formAnnotation: function formAnnotation(name, type, x, y, w, h) {
    var options = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : {};

    var fieldDict = this._fieldDict(name, type, options);

    fieldDict.Subtype = 'Widget';

    if (fieldDict.F === undefined) {
      fieldDict.F = 4; // print the annotation
    } // Add Field annot to page, and get it's ref


    this.annotate(x, y, w, h, fieldDict);
    var annotRef = this.page.annotations[this.page.annotations.length - 1];
    return this._addToParent(annotRef);
  },
  formText: function formText(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'text', x, y, w, h, options);
  },
  formPushButton: function formPushButton(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'pushButton', x, y, w, h, options);
  },
  formCombo: function formCombo(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'combo', x, y, w, h, options);
  },
  formList: function formList(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'list', x, y, w, h, options);
  },
  formRadioButton: function formRadioButton(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'radioButton', x, y, w, h, options);
  },
  formCheckbox: function formCheckbox(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'checkbox', x, y, w, h, options);
  },
  _addToParent: function _addToParent(fieldRef) {
    var parent = fieldRef.data.Parent;

    if (parent) {
      if (!parent.data.Kids) {
        parent.data.Kids = [];
      }

      parent.data.Kids.push(fieldRef);
    } else {
      this._root.data.AcroForm.data.Fields.push(fieldRef);
    }

    return this;
  },
  _fieldDict: function _fieldDict(name, type) {
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    if (!this._acroform) {
      throw new Error('Call document.initForms() method before adding form elements to document');
    }

    var opts = Object.assign({}, options);

    if (type !== null) {
      opts = this._resolveType(type, options);
    }

    opts = this._resolveFlags(opts);
    opts = this._resolveJustify(opts);
    opts = this._resolveFont(opts);
    opts = this._resolveStrings(opts);
    opts = this._resolveColors(opts);
    opts = this._resolveFormat(opts);
    opts.T = new String(name);

    if (opts.parent) {
      opts.Parent = opts.parent;
      delete opts.parent;
    }

    return opts;
  },
  _resolveType: function _resolveType(type, opts) {
    if (type === 'text') {
      opts.FT = 'Tx';
    } else if (type === 'pushButton') {
      opts.FT = 'Btn';
      opts.pushButton = true;
    } else if (type === 'radioButton') {
      opts.FT = 'Btn';
      opts.radioButton = true;
    } else if (type === 'checkbox') {
      opts.FT = 'Btn';
    } else if (type === 'combo') {
      opts.FT = 'Ch';
      opts.combo = true;
    } else if (type === 'list') {
      opts.FT = 'Ch';
    } else {
      throw new Error("Invalid form annotation type '".concat(type, "'"));
    }

    return opts;
  },
  _resolveFormat: function _resolveFormat(opts) {
    var f = opts.format;

    if (f && f.type) {
      var fnKeystroke;
      var fnFormat;
      var params = '';

      if (FORMAT_SPECIAL[f.type] !== undefined) {
        fnKeystroke = "AFSpecial_Keystroke";
        fnFormat = "AFSpecial_Format";
        params = FORMAT_SPECIAL[f.type];
      } else {
        var format = f.type.charAt(0).toUpperCase() + f.type.slice(1);
        fnKeystroke = "AF".concat(format, "_Keystroke");
        fnFormat = "AF".concat(format, "_Format");

        if (f.type === 'date') {
          fnKeystroke += 'Ex';
          params = String(f.param);
        } else if (f.type === 'time') {
          params = String(f.param);
        } else if (f.type === 'number') {
          var p = Object.assign({}, FORMAT_DEFAULT.number, f);
          params = String([String(p.nDec), p.sepComma ? '0' : '1', '"' + p.negStyle + '"', 'null', '"' + p.currency + '"', String(p.currencyPrepend)].join(','));
        } else if (f.type === 'percent') {
          var _p = Object.assign({}, FORMAT_DEFAULT.percent, f);

          params = String([String(_p.nDec), _p.sepComma ? '0' : '1'].join(','));
        }
      }

      opts.AA = opts.AA ? opts.AA : {};
      opts.AA.K = {
        S: 'JavaScript',
        JS: new String("".concat(fnKeystroke, "(").concat(params, ");"))
      };
      opts.AA.F = {
        S: 'JavaScript',
        JS: new String("".concat(fnFormat, "(").concat(params, ");"))
      };
    }

    delete opts.format;
    return opts;
  },
  _resolveColors: function _resolveColors(opts) {
    var color = this._normalizeColor(opts.backgroundColor);

    if (color) {
      if (!opts.MK) {
        opts.MK = {};
      }

      opts.MK.BG = color;
    }

    color = this._normalizeColor(opts.borderColor);

    if (color) {
      if (!opts.MK) {
        opts.MK = {};
      }

      opts.MK.BC = color;
    }

    delete opts.backgroundColor;
    delete opts.borderColor;
    return opts;
  },
  _resolveFlags: function _resolveFlags(options) {
    var result = 0;
    Object.keys(options).forEach(function (key) {
      if (FIELD_FLAGS[key]) {
        result |= FIELD_FLAGS[key];
        delete options[key];
      }
    });

    if (result !== 0) {
      options.Ff = options.Ff ? options.Ff : 0;
      options.Ff |= result;
    }

    return options;
  },
  _resolveJustify: function _resolveJustify(options) {
    var result = 0;

    if (options.align !== undefined) {
      if (typeof FIELD_JUSTIFY[options.align] === 'number') {
        result = FIELD_JUSTIFY[options.align];
      }

      delete options.align;
    }

    if (result !== 0) {
      options.Q = result; // default
    }

    return options;
  },
  _resolveFont: function _resolveFont(options) {
    // add current font to document-level AcroForm dict if necessary
    if (this._acroform.fonts[this._font.id] === null) {
      this._acroform.fonts[this._font.id] = this._font.ref();
    } // add current font to field's resource dict (RD) if not the default acroform font


    if (this._acroform.defaultFont !== this._font.name) {
      options.DR = {
        Font: {}
      };
      options.DR.Font[this._font.id] = this._font.ref();
      options.DA = new String("/".concat(this._font.id, " 0 Tf 0 g"));
    }

    return options;
  },
  _resolveStrings: function _resolveStrings(options) {
    var select = [];

    function appendChoices(a) {
      if (Array.isArray(a)) {
        for (var idx = 0; idx < a.length; idx++) {
          if (typeof a[idx] === 'string') {
            select.push(new String(a[idx]));
          } else {
            select.push(a[idx]);
          }
        }
      }
    }

    appendChoices(options.Opt);

    if (options.select) {
      appendChoices(options.select);
      delete options.select;
    }

    if (select.length) {
      options.Opt = select;
    }

    if (options.value || options.defaultValue) ;
    Object.keys(VALUE_MAP).forEach(function (key) {
      if (options[key] !== undefined) {
        options[VALUE_MAP[key]] = options[key];
        delete options[key];
      }
    });
    ['V', 'DV'].forEach(function (key) {
      if (typeof options[key] === 'string') {
        options[key] = new String(options[key]);
      }
    });

    if (options.MK && options.MK.CA) {
      options.MK.CA = new String(options.MK.CA);
    }

    if (options.label) {
      options.MK = options.MK ? options.MK : {};
      options.MK.CA = new String(options.label);
      delete options.label;
    }

    return options;
  }
};

var PDFDocument = /*#__PURE__*/function (_stream$Readable) {
  _inherits(PDFDocument, _stream$Readable);

  function PDFDocument() {
    var _this;

    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    _classCallCheck(this, PDFDocument);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(PDFDocument).call(this, options));
    _this.options = options; // PDF version

    switch (options.pdfVersion) {
      case '1.4':
        _this.version = 1.4;
        break;

      case '1.5':
        _this.version = 1.5;
        break;

      case '1.6':
        _this.version = 1.6;
        break;

      case '1.7':
      case '1.7ext3':
        _this.version = 1.7;
        break;

      default:
        _this.version = 1.3;
        break;
    } // Whether streams should be compressed


    _this.compress = _this.options.compress != null ? _this.options.compress : true;
    _this._pageBuffer = [];
    _this._pageBufferStart = 0; // The PDF object store

    _this._offsets = [];
    _this._waiting = 0;
    _this._ended = false;
    _this._offset = 0;

    var Pages = _this.ref({
      Type: 'Pages',
      Count: 0,
      Kids: []
    });

    var Names = _this.ref({
      Dests: new PDFNameTree()
    });

    _this._root = _this.ref({
      Type: 'Catalog',
      Pages: Pages,
      Names: Names
    }); // The current page

    _this.page = null; // Initialize mixins

    _this.initColor();

    _this.initVector();

    _this.initFonts(options.font);

    _this.initText();

    _this.initImages();

    _this.initOutline(); // Initialize the metadata


    _this.info = {
      Producer: 'PDFKit',
      Creator: 'PDFKit',
      CreationDate: new Date()
    };

    if (_this.options.info) {
      for (var key in _this.options.info) {
        var val = _this.options.info[key];
        _this.info[key] = val;
      }
    } // Generate file ID


    _this._id = PDFSecurity.generateFileID(_this.info); // Initialize security settings

    _this._security = PDFSecurity.create(_assertThisInitialized(_this), options); // Write the header
    // PDF version

    _this._write("%PDF-".concat(_this.version)); // 4 binary chars, as recommended by the spec


    _this._write('%\xFF\xFF\xFF\xFF'); // Add the first page


    if (_this.options.autoFirstPage !== false) {
      _this.addPage();
    }

    return _this;
  }

  _createClass(PDFDocument, [{
    key: "addPage",
    value: function addPage(options) {
      // end the current page if needed
      if (options == null) {
        options = this.options;
      }

      if (!this.options.bufferPages) {
        this.flushPages();
      } // create a page object


      this.page = new PDFPage(this, options);

      this._pageBuffer.push(this.page); // add the page to the object store


      var pages = this._root.data.Pages.data;
      pages.Kids.push(this.page.dictionary);
      pages.Count++; // reset x and y coordinates

      this.x = this.page.margins.left;
      this.y = this.page.margins.top; // flip PDF coordinate system so that the origin is in
      // the top left rather than the bottom left

      this._ctm = [1, 0, 0, 1, 0, 0];
      this.transform(1, 0, 0, -1, 0, this.page.height);
      this.emit('pageAdded');
      return this;
    }
  }, {
    key: "bufferedPageRange",
    value: function bufferedPageRange() {
      return {
        start: this._pageBufferStart,
        count: this._pageBuffer.length
      };
    }
  }, {
    key: "switchToPage",
    value: function switchToPage(n) {
      var page;

      if (!(page = this._pageBuffer[n - this._pageBufferStart])) {
        throw new Error("switchToPage(".concat(n, ") out of bounds, current buffer covers pages ").concat(this._pageBufferStart, " to ").concat(this._pageBufferStart + this._pageBuffer.length - 1));
      }

      return this.page = page;
    }
  }, {
    key: "flushPages",
    value: function flushPages() {
      // this local variable exists so we're future-proof against
      // reentrant calls to flushPages.
      var pages = this._pageBuffer;
      this._pageBuffer = [];
      this._pageBufferStart += pages.length;
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = pages[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var page = _step.value;
          page.end();
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }
    }
  }, {
    key: "addNamedDestination",
    value: function addNamedDestination(name) {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      if (args.length === 0) {
        args = ['XYZ', null, null, null];
      }

      if (args[0] === 'XYZ' && args[2] !== null) {
        args[2] = this.page.height - args[2];
      }

      args.unshift(this.page.dictionary);

      this._root.data.Names.data.Dests.add(name, args);
    }
  }, {
    key: "addNamedJavaScript",
    value: function addNamedJavaScript(name, js) {
      if (!this._root.data.Names.data.JavaScript) {
        this._root.data.Names.data.JavaScript = new PDFNameTree();
      }

      var data = {
        JS: new String(js),
        S: 'JavaScript'
      };

      this._root.data.Names.data.JavaScript.add(name, data);
    }
  }, {
    key: "ref",
    value: function ref(data) {
      var ref = new PDFReference(this, this._offsets.length + 1, data);

      this._offsets.push(null); // placeholder for this object's offset once it is finalized


      this._waiting++;
      return ref;
    }
  }, {
    key: "_read",
    value: function _read() {} // do nothing, but this method is required by node

  }, {
    key: "_write",
    value: function _write(data) {
      if (!Buffer.isBuffer(data)) {
        data = new Buffer(data + '\n', 'binary');
      }

      this.push(data);
      return this._offset += data.length;
    }
  }, {
    key: "addContent",
    value: function addContent(data) {
      this.page.write(data);
      return this;
    }
  }, {
    key: "_refEnd",
    value: function _refEnd(ref) {
      this._offsets[ref.id - 1] = ref.offset;

      if (--this._waiting === 0 && this._ended) {
        this._finalize();

        return this._ended = false;
      }
    }
  }, {
    key: "write",
    value: function write(filename, fn) {
      // print a deprecation warning with a stacktrace
      var err = new Error("PDFDocument#write is deprecated, and will be removed in a future version of PDFKit. Please pipe the document into a Node stream.");
      console.warn(err.stack);
      this.pipe(fs.createWriteStream(filename));
      this.end();
      return this.once('end', fn);
    }
  }, {
    key: "end",
    value: function end() {
      this.flushPages();
      this._info = this.ref();

      for (var key in this.info) {
        var val = this.info[key];

        if (typeof val === 'string') {
          val = new String(val);
        }

        var entry = this.ref(val);
        entry.end();
        this._info.data[key] = entry;
      }

      this._info.end();

      for (var name in this._fontFamilies) {
        var font = this._fontFamilies[name];
        font.finalize();
      }

      this.endOutline();

      this._root.end();

      this._root.data.Pages.end();

      this._root.data.Names.end();

      this.endAcroForm();

      if (this._security) {
        this._security.end();
      }

      if (this._waiting === 0) {
        return this._finalize();
      } else {
        return this._ended = true;
      }
    }
  }, {
    key: "_finalize",
    value: function _finalize(fn) {
      // generate xref
      var xRefOffset = this._offset;

      this._write('xref');

      this._write("0 ".concat(this._offsets.length + 1));

      this._write('0000000000 65535 f ');

      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;

      try {
        for (var _iterator2 = this._offsets[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          var offset = _step2.value;
          offset = "0000000000".concat(offset).slice(-10);

          this._write(offset + ' 00000 n ');
        } // trailer

      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
            _iterator2.return();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }

      var trailer = {
        Size: this._offsets.length + 1,
        Root: this._root,
        Info: this._info,
        ID: [this._id, this._id]
      };

      if (this._security) {
        trailer.Encrypt = this._security.dictionary;
      }

      this._write('trailer');

      this._write(PDFObject.convert(trailer));

      this._write('startxref');

      this._write("".concat(xRefOffset));

      this._write('%%EOF'); // end the stream


      return this.push(null);
    }
  }, {
    key: "toString",
    value: function toString() {
      return '[object PDFDocument]';
    }
  }]);

  return PDFDocument;
}(_stream.default.Readable);

var mixin = function mixin(methods) {
  Object.assign(PDFDocument.prototype, methods);
};

mixin(ColorMixin);
mixin(VectorMixin);
mixin(FontsMixin);
mixin(TextMixin);
mixin(ImagesMixin);
mixin(AnnotationsMixin);
mixin(OutlineMixin);
mixin(AcroFormMixin);
var _default = PDFDocument;
exports.default = _default;
/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer, "/"))

/***/ }),
/* 308 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var inspectSource = __webpack_require__(140);

var WeakMap = global.WeakMap;

module.exports = typeof WeakMap === 'function' && /native code/.test(inspectSource(WeakMap));


/***/ }),
/* 309 */
/***/ (function(module, exports, __webpack_require__) {

var getBuiltIn = __webpack_require__(36);
var getOwnPropertyNamesModule = __webpack_require__(57);
var getOwnPropertySymbolsModule = __webpack_require__(143);
var anObject = __webpack_require__(16);

// all object keys, includes non-enumerable and symbols
module.exports = getBuiltIn('Reflect', 'ownKeys') || function ownKeys(it) {
  var keys = getOwnPropertyNamesModule.f(anObject(it));
  var getOwnPropertySymbols = getOwnPropertySymbolsModule.f;
  return getOwnPropertySymbols ? keys.concat(getOwnPropertySymbols(it)) : keys;
};


/***/ }),
/* 310 */
/***/ (function(module, exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(11);
var definePropertyModule = __webpack_require__(15);
var anObject = __webpack_require__(16);
var objectKeys = __webpack_require__(94);

// `Object.defineProperties` method
// https://tc39.github.io/ecma262/#sec-object.defineproperties
module.exports = DESCRIPTORS ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject(O);
  var keys = objectKeys(Properties);
  var length = keys.length;
  var index = 0;
  var key;
  while (length > index) definePropertyModule.f(O, key = keys[index++], Properties[key]);
  return O;
};


/***/ }),
/* 311 */
/***/ (function(module, exports, __webpack_require__) {

var toIndexedObject = __webpack_require__(27);
var nativeGetOwnPropertyNames = __webpack_require__(57).f;

var toString = {}.toString;

var windowNames = typeof window == 'object' && window && Object.getOwnPropertyNames
  ? Object.getOwnPropertyNames(window) : [];

var getWindowNames = function (it) {
  try {
    return nativeGetOwnPropertyNames(it);
  } catch (error) {
    return windowNames.slice();
  }
};

// fallback for IE11 buggy Object.getOwnPropertyNames with iframe and window
module.exports.f = function getOwnPropertyNames(it) {
  return windowNames && toString.call(it) == '[object Window]'
    ? getWindowNames(it)
    : nativeGetOwnPropertyNames(toIndexedObject(it));
};


/***/ }),
/* 312 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var $every = __webpack_require__(20).every;
var arrayMethodIsStrict = __webpack_require__(59);
var arrayMethodUsesToLength = __webpack_require__(31);

var STRICT_METHOD = arrayMethodIsStrict('every');
var USES_TO_LENGTH = arrayMethodUsesToLength('every');

// `Array.prototype.every` method
// https://tc39.github.io/ecma262/#sec-array.prototype.every
$({ target: 'Array', proto: true, forced: !STRICT_METHOD || !USES_TO_LENGTH }, {
  every: function every(callbackfn /* , thisArg */) {
    return $every(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});


/***/ }),
/* 313 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var $filter = __webpack_require__(20).filter;
var arrayMethodHasSpeciesSupport = __webpack_require__(116);
var arrayMethodUsesToLength = __webpack_require__(31);

var HAS_SPECIES_SUPPORT = arrayMethodHasSpeciesSupport('filter');
// Edge 14- issue
var USES_TO_LENGTH = arrayMethodUsesToLength('filter');

// `Array.prototype.filter` method
// https://tc39.github.io/ecma262/#sec-array.prototype.filter
// with adding support of @@species
$({ target: 'Array', proto: true, forced: !HAS_SPECIES_SUPPORT || !USES_TO_LENGTH }, {
  filter: function filter(callbackfn /* , thisArg */) {
    return $filter(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});


/***/ }),
/* 314 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var forEach = __webpack_require__(212);

// `Array.prototype.forEach` method
// https://tc39.github.io/ecma262/#sec-array.prototype.foreach
$({ target: 'Array', proto: true, forced: [].forEach != forEach }, {
  forEach: forEach
});


/***/ }),
/* 315 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var bind = __webpack_require__(96);
var toObject = __webpack_require__(19);
var callWithSafeIterationClosing = __webpack_require__(214);
var isArrayIteratorMethod = __webpack_require__(150);
var toLength = __webpack_require__(8);
var createProperty = __webpack_require__(146);
var getIteratorMethod = __webpack_require__(151);

// `Array.from` method implementation
// https://tc39.github.io/ecma262/#sec-array.from
module.exports = function from(arrayLike /* , mapfn = undefined, thisArg = undefined */) {
  var O = toObject(arrayLike);
  var C = typeof this == 'function' ? this : Array;
  var argumentsLength = arguments.length;
  var mapfn = argumentsLength > 1 ? arguments[1] : undefined;
  var mapping = mapfn !== undefined;
  var iteratorMethod = getIteratorMethod(O);
  var index = 0;
  var length, result, step, iterator, next, value;
  if (mapping) mapfn = bind(mapfn, argumentsLength > 2 ? arguments[2] : undefined, 2);
  // if the target is not iterable or it's an array with the default iterator - use a simple case
  if (iteratorMethod != undefined && !(C == Array && isArrayIteratorMethod(iteratorMethod))) {
    iterator = iteratorMethod.call(O);
    next = iterator.next;
    result = new C();
    for (;!(step = next.call(iterator)).done; index++) {
      value = mapping ? callWithSafeIterationClosing(iterator, mapfn, [step.value, index], true) : step.value;
      createProperty(result, index, value);
    }
  } else {
    length = toLength(O.length);
    result = new C(length);
    for (;length > index; index++) {
      value = mapping ? mapfn(O[index], index) : O[index];
      createProperty(result, index, value);
    }
  }
  result.length = index;
  return result;
};


/***/ }),
/* 316 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var IteratorPrototype = __webpack_require__(217).IteratorPrototype;
var create = __webpack_require__(58);
var createPropertyDescriptor = __webpack_require__(42);
var setToStringTag = __webpack_require__(95);
var Iterators = __webpack_require__(97);

var returnThis = function () { return this; };

module.exports = function (IteratorConstructor, NAME, next) {
  var TO_STRING_TAG = NAME + ' Iterator';
  IteratorConstructor.prototype = create(IteratorPrototype, { next: createPropertyDescriptor(1, next) });
  setToStringTag(IteratorConstructor, TO_STRING_TAG, false, true);
  Iterators[TO_STRING_TAG] = returnThis;
  return IteratorConstructor;
};


/***/ }),
/* 317 */
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__(13);

module.exports = function (it) {
  if (!isObject(it) && it !== null) {
    throw TypeError("Can't set " + String(it) + ' as a prototype');
  } return it;
};


/***/ }),
/* 318 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var $map = __webpack_require__(20).map;
var arrayMethodHasSpeciesSupport = __webpack_require__(116);
var arrayMethodUsesToLength = __webpack_require__(31);

var HAS_SPECIES_SUPPORT = arrayMethodHasSpeciesSupport('map');
// FF49- issue
var USES_TO_LENGTH = arrayMethodUsesToLength('map');

// `Array.prototype.map` method
// https://tc39.github.io/ecma262/#sec-array.prototype.map
// with adding support of @@species
$({ target: 'Array', proto: true, forced: !HAS_SPECIES_SUPPORT || !USES_TO_LENGTH }, {
  map: function map(callbackfn /* , thisArg */) {
    return $map(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});


/***/ }),
/* 319 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var $some = __webpack_require__(20).some;
var arrayMethodIsStrict = __webpack_require__(59);
var arrayMethodUsesToLength = __webpack_require__(31);

var STRICT_METHOD = arrayMethodIsStrict('some');
var USES_TO_LENGTH = arrayMethodUsesToLength('some');

// `Array.prototype.some` method
// https://tc39.github.io/ecma262/#sec-array.prototype.some
$({ target: 'Array', proto: true, forced: !STRICT_METHOD || !USES_TO_LENGTH }, {
  some: function some(callbackfn /* , thisArg */) {
    return $some(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});


/***/ }),
/* 320 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var global = __webpack_require__(3);
var arrayBufferModule = __webpack_require__(154);
var setSpecies = __webpack_require__(156);

var ARRAY_BUFFER = 'ArrayBuffer';
var ArrayBuffer = arrayBufferModule[ARRAY_BUFFER];
var NativeArrayBuffer = global[ARRAY_BUFFER];

// `ArrayBuffer` constructor
// https://tc39.github.io/ecma262/#sec-arraybuffer-constructor
$({ global: true, forced: NativeArrayBuffer !== ArrayBuffer }, {
  ArrayBuffer: ArrayBuffer
});

setSpecies(ARRAY_BUFFER);


/***/ }),
/* 321 */
/***/ (function(module, exports) {

// IEEE754 conversions based on https://github.com/feross/ieee754
// eslint-disable-next-line no-shadow-restricted-names
var Infinity = 1 / 0;
var abs = Math.abs;
var pow = Math.pow;
var floor = Math.floor;
var log = Math.log;
var LN2 = Math.LN2;

var pack = function (number, mantissaLength, bytes) {
  var buffer = new Array(bytes);
  var exponentLength = bytes * 8 - mantissaLength - 1;
  var eMax = (1 << exponentLength) - 1;
  var eBias = eMax >> 1;
  var rt = mantissaLength === 23 ? pow(2, -24) - pow(2, -77) : 0;
  var sign = number < 0 || number === 0 && 1 / number < 0 ? 1 : 0;
  var index = 0;
  var exponent, mantissa, c;
  number = abs(number);
  // eslint-disable-next-line no-self-compare
  if (number != number || number === Infinity) {
    // eslint-disable-next-line no-self-compare
    mantissa = number != number ? 1 : 0;
    exponent = eMax;
  } else {
    exponent = floor(log(number) / LN2);
    if (number * (c = pow(2, -exponent)) < 1) {
      exponent--;
      c *= 2;
    }
    if (exponent + eBias >= 1) {
      number += rt / c;
    } else {
      number += rt * pow(2, 1 - eBias);
    }
    if (number * c >= 2) {
      exponent++;
      c /= 2;
    }
    if (exponent + eBias >= eMax) {
      mantissa = 0;
      exponent = eMax;
    } else if (exponent + eBias >= 1) {
      mantissa = (number * c - 1) * pow(2, mantissaLength);
      exponent = exponent + eBias;
    } else {
      mantissa = number * pow(2, eBias - 1) * pow(2, mantissaLength);
      exponent = 0;
    }
  }
  for (; mantissaLength >= 8; buffer[index++] = mantissa & 255, mantissa /= 256, mantissaLength -= 8);
  exponent = exponent << mantissaLength | mantissa;
  exponentLength += mantissaLength;
  for (; exponentLength > 0; buffer[index++] = exponent & 255, exponent /= 256, exponentLength -= 8);
  buffer[--index] |= sign * 128;
  return buffer;
};

var unpack = function (buffer, mantissaLength) {
  var bytes = buffer.length;
  var exponentLength = bytes * 8 - mantissaLength - 1;
  var eMax = (1 << exponentLength) - 1;
  var eBias = eMax >> 1;
  var nBits = exponentLength - 7;
  var index = bytes - 1;
  var sign = buffer[index--];
  var exponent = sign & 127;
  var mantissa;
  sign >>= 7;
  for (; nBits > 0; exponent = exponent * 256 + buffer[index], index--, nBits -= 8);
  mantissa = exponent & (1 << -nBits) - 1;
  exponent >>= -nBits;
  nBits += mantissaLength;
  for (; nBits > 0; mantissa = mantissa * 256 + buffer[index], index--, nBits -= 8);
  if (exponent === 0) {
    exponent = 1 - eBias;
  } else if (exponent === eMax) {
    return mantissa ? NaN : sign ? -Infinity : Infinity;
  } else {
    mantissa = mantissa + pow(2, mantissaLength);
    exponent = exponent - eBias;
  } return (sign ? -1 : 1) * mantissa * pow(2, exponent - mantissaLength);
};

module.exports = {
  pack: pack,
  unpack: unpack
};


/***/ }),
/* 322 */
/***/ (function(module, exports, __webpack_require__) {

var $ = __webpack_require__(1);
var numberIsFinite = __webpack_require__(323);

// `Number.isFinite` method
// https://tc39.github.io/ecma262/#sec-number.isfinite
$({ target: 'Number', stat: true }, { isFinite: numberIsFinite });


/***/ }),
/* 323 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);

var globalIsFinite = global.isFinite;

// `Number.isFinite` method
// https://tc39.github.io/ecma262/#sec-number.isfinite
module.exports = Number.isFinite || function isFinite(it) {
  return typeof it == 'number' && globalIsFinite(it);
};


/***/ }),
/* 324 */
/***/ (function(module, exports, __webpack_require__) {

var $ = __webpack_require__(1);
var assign = __webpack_require__(325);

// `Object.assign` method
// https://tc39.github.io/ecma262/#sec-object.assign
$({ target: 'Object', stat: true, forced: Object.assign !== assign }, {
  assign: assign
});


/***/ }),
/* 325 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var DESCRIPTORS = __webpack_require__(11);
var fails = __webpack_require__(4);
var objectKeys = __webpack_require__(94);
var getOwnPropertySymbolsModule = __webpack_require__(143);
var propertyIsEnumerableModule = __webpack_require__(110);
var toObject = __webpack_require__(19);
var IndexedObject = __webpack_require__(92);

var nativeAssign = Object.assign;
var defineProperty = Object.defineProperty;

// `Object.assign` method
// https://tc39.github.io/ecma262/#sec-object.assign
module.exports = !nativeAssign || fails(function () {
  // should have correct order of operations (Edge bug)
  if (DESCRIPTORS && nativeAssign({ b: 1 }, nativeAssign(defineProperty({}, 'a', {
    enumerable: true,
    get: function () {
      defineProperty(this, 'b', {
        value: 3,
        enumerable: false
      });
    }
  }), { b: 2 })).b !== 1) return true;
  // should work with symbols and should have deterministic property order (V8 bug)
  var A = {};
  var B = {};
  // eslint-disable-next-line no-undef
  var symbol = Symbol();
  var alphabet = 'abcdefghijklmnopqrst';
  A[symbol] = 7;
  alphabet.split('').forEach(function (chr) { B[chr] = chr; });
  return nativeAssign({}, A)[symbol] != 7 || objectKeys(nativeAssign({}, B)).join('') != alphabet;
}) ? function assign(target, source) { // eslint-disable-line no-unused-vars
  var T = toObject(target);
  var argumentsLength = arguments.length;
  var index = 1;
  var getOwnPropertySymbols = getOwnPropertySymbolsModule.f;
  var propertyIsEnumerable = propertyIsEnumerableModule.f;
  while (argumentsLength > index) {
    var S = IndexedObject(arguments[index++]);
    var keys = getOwnPropertySymbols ? objectKeys(S).concat(getOwnPropertySymbols(S)) : objectKeys(S);
    var length = keys.length;
    var j = 0;
    var key;
    while (length > j) {
      key = keys[j++];
      if (!DESCRIPTORS || propertyIsEnumerable.call(S, key)) T[key] = S[key];
    }
  } return T;
} : nativeAssign;


/***/ }),
/* 326 */
/***/ (function(module, exports, __webpack_require__) {

var $ = __webpack_require__(1);
var fails = __webpack_require__(4);
var toObject = __webpack_require__(19);
var nativeGetPrototypeOf = __webpack_require__(60);
var CORRECT_PROTOTYPE_GETTER = __webpack_require__(218);

var FAILS_ON_PRIMITIVES = fails(function () { nativeGetPrototypeOf(1); });

// `Object.getPrototypeOf` method
// https://tc39.github.io/ecma262/#sec-object.getprototypeof
$({ target: 'Object', stat: true, forced: FAILS_ON_PRIMITIVES, sham: !CORRECT_PROTOTYPE_GETTER }, {
  getPrototypeOf: function getPrototypeOf(it) {
    return nativeGetPrototypeOf(toObject(it));
  }
});



/***/ }),
/* 327 */
/***/ (function(module, exports, __webpack_require__) {

var $ = __webpack_require__(1);
var setPrototypeOf = __webpack_require__(45);

// `Object.setPrototypeOf` method
// https://tc39.github.io/ecma262/#sec-object.setprototypeof
$({ target: 'Object', stat: true }, {
  setPrototypeOf: setPrototypeOf
});


/***/ }),
/* 328 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var TO_STRING_TAG_SUPPORT = __webpack_require__(152);
var classof = __webpack_require__(117);

// `Object.prototype.toString` method implementation
// https://tc39.github.io/ecma262/#sec-object.prototype.tostring
module.exports = TO_STRING_TAG_SUPPORT ? {}.toString : function toString() {
  return '[object ' + classof(this) + ']';
};


/***/ }),
/* 329 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var fails = __webpack_require__(4);

// babel-minify transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError,
// so we use an intermediate function.
function RE(s, f) {
  return RegExp(s, f);
}

exports.UNSUPPORTED_Y = fails(function () {
  // babel-minify transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError
  var re = RE('a', 'y');
  re.lastIndex = 2;
  return re.exec('abcd') != null;
});

exports.BROKEN_CARET = fails(function () {
  // https://bugzilla.mozilla.org/show_bug.cgi?id=773687
  var re = RE('^r', 'gy');
  re.lastIndex = 2;
  return re.exec('str') != null;
});


/***/ }),
/* 330 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var codeAt = __webpack_require__(158).codeAt;

// `String.prototype.codePointAt` method
// https://tc39.github.io/ecma262/#sec-string.prototype.codepointat
$({ target: 'String', proto: true }, {
  codePointAt: function codePointAt(pos) {
    return codeAt(this, pos);
  }
});


/***/ }),
/* 331 */
/***/ (function(module, exports, __webpack_require__) {

var $ = __webpack_require__(1);
var toAbsoluteIndex = __webpack_require__(43);

var fromCharCode = String.fromCharCode;
var nativeFromCodePoint = String.fromCodePoint;

// length should be 1, old FF problem
var INCORRECT_LENGTH = !!nativeFromCodePoint && nativeFromCodePoint.length != 1;

// `String.fromCodePoint` method
// https://tc39.github.io/ecma262/#sec-string.fromcodepoint
$({ target: 'String', stat: true, forced: INCORRECT_LENGTH }, {
  fromCodePoint: function fromCodePoint(x) { // eslint-disable-line no-unused-vars
    var elements = [];
    var length = arguments.length;
    var i = 0;
    var code;
    while (length > i) {
      code = +arguments[i++];
      if (toAbsoluteIndex(code, 0x10FFFF) !== code) throw RangeError(code + ' is not a valid code point');
      elements.push(code < 0x10000
        ? fromCharCode(code)
        : fromCharCode(((code -= 0x10000) >> 10) + 0xD800, code % 0x400 + 0xDC00)
      );
    } return elements.join('');
  }
});


/***/ }),
/* 332 */
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__(13);
var classof = __webpack_require__(30);
var wellKnownSymbol = __webpack_require__(6);

var MATCH = wellKnownSymbol('match');

// `IsRegExp` abstract operation
// https://tc39.github.io/ecma262/#sec-isregexp
module.exports = function (it) {
  var isRegExp;
  return isObject(it) && ((isRegExp = it[MATCH]) !== undefined ? !!isRegExp : classof(it) == 'RegExp');
};


/***/ }),
/* 333 */
/***/ (function(module, exports, __webpack_require__) {

var fails = __webpack_require__(4);
var whitespaces = __webpack_require__(226);

var non = '\u200B\u0085\u180E';

// check that a method works with the correct list
// of whitespaces and has a correct name
module.exports = function (METHOD_NAME) {
  return fails(function () {
    return !!whitespaces[METHOD_NAME]() || non[METHOD_NAME]() != non || whitespaces[METHOD_NAME].name !== METHOD_NAME;
  });
};


/***/ }),
/* 334 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var createHTML = __webpack_require__(234);
var forcedStringHTMLMethod = __webpack_require__(235);

// `String.prototype.strike` method
// https://tc39.github.io/ecma262/#sec-string.prototype.strike
$({ target: 'String', proto: true, forced: forcedStringHTMLMethod('strike') }, {
  strike: function strike() {
    return createHTML(this, 'strike', '', '');
  }
});


/***/ }),
/* 335 */
/***/ (function(module, exports, __webpack_require__) {

/* eslint-disable no-new */
var global = __webpack_require__(3);
var fails = __webpack_require__(4);
var checkCorrectnessOfIteration = __webpack_require__(153);
var NATIVE_ARRAY_BUFFER_VIEWS = __webpack_require__(5).NATIVE_ARRAY_BUFFER_VIEWS;

var ArrayBuffer = global.ArrayBuffer;
var Int8Array = global.Int8Array;

module.exports = !NATIVE_ARRAY_BUFFER_VIEWS || !fails(function () {
  Int8Array(1);
}) || !fails(function () {
  new Int8Array(-1);
}) || !checkCorrectnessOfIteration(function (iterable) {
  new Int8Array();
  new Int8Array(null);
  new Int8Array(1.5);
  new Int8Array(iterable);
}, true) || fails(function () {
  // Safari (11+) bug - a reason why even Safari 13 should load a typed array polyfill
  return new Int8Array(new ArrayBuffer(2), 1, undefined).length !== 1;
});


/***/ }),
/* 336 */
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__(37);

module.exports = function (it) {
  var result = toInteger(it);
  if (result < 0) throw RangeError("The argument can't be less than 0");
  return result;
};


/***/ }),
/* 337 */
/***/ (function(module, exports, __webpack_require__) {

var toObject = __webpack_require__(19);
var toLength = __webpack_require__(8);
var getIteratorMethod = __webpack_require__(151);
var isArrayIteratorMethod = __webpack_require__(150);
var bind = __webpack_require__(96);
var aTypedArrayConstructor = __webpack_require__(5).aTypedArrayConstructor;

module.exports = function from(source /* , mapfn, thisArg */) {
  var O = toObject(source);
  var argumentsLength = arguments.length;
  var mapfn = argumentsLength > 1 ? arguments[1] : undefined;
  var mapping = mapfn !== undefined;
  var iteratorMethod = getIteratorMethod(O);
  var i, length, result, step, iterator, next;
  if (iteratorMethod != undefined && !isArrayIteratorMethod(iteratorMethod)) {
    iterator = iteratorMethod.call(O);
    next = iterator.next;
    O = [];
    while (!(step = next.call(iterator)).done) {
      O.push(step.value);
    }
  }
  if (mapping && argumentsLength > 2) {
    mapfn = bind(mapfn, arguments[2], 2);
  }
  length = toLength(O.length);
  result = new (aTypedArrayConstructor(this))(length);
  for (i = 0; length > i; i++) {
    result[i] = mapping ? mapfn(O[i], i) : O[i];
  }
  return result;
};


/***/ }),
/* 338 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var toObject = __webpack_require__(19);
var toAbsoluteIndex = __webpack_require__(43);
var toLength = __webpack_require__(8);

var min = Math.min;

// `Array.prototype.copyWithin` method implementation
// https://tc39.github.io/ecma262/#sec-array.prototype.copywithin
module.exports = [].copyWithin || function copyWithin(target /* = 0 */, start /* = 0, end = @length */) {
  var O = toObject(this);
  var len = toLength(O.length);
  var to = toAbsoluteIndex(target, len);
  var from = toAbsoluteIndex(start, len);
  var end = arguments.length > 2 ? arguments[2] : undefined;
  var count = min((end === undefined ? len : toAbsoluteIndex(end, len)) - from, len - to);
  var inc = 1;
  if (from < to && to < from + count) {
    inc = -1;
    from += count - 1;
    to += count - 1;
  }
  while (count-- > 0) {
    if (from in O) O[to] = O[from];
    else delete O[to];
    to += inc;
    from += inc;
  } return O;
};


/***/ }),
/* 339 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var toIndexedObject = __webpack_require__(27);
var toInteger = __webpack_require__(37);
var toLength = __webpack_require__(8);
var arrayMethodIsStrict = __webpack_require__(59);
var arrayMethodUsesToLength = __webpack_require__(31);

var min = Math.min;
var nativeLastIndexOf = [].lastIndexOf;
var NEGATIVE_ZERO = !!nativeLastIndexOf && 1 / [1].lastIndexOf(1, -0) < 0;
var STRICT_METHOD = arrayMethodIsStrict('lastIndexOf');
// For preventing possible almost infinite loop in non-standard implementations, test the forward version of the method
var USES_TO_LENGTH = arrayMethodUsesToLength('indexOf', { ACCESSORS: true, 1: 0 });
var FORCED = NEGATIVE_ZERO || !STRICT_METHOD || !USES_TO_LENGTH;

// `Array.prototype.lastIndexOf` method implementation
// https://tc39.github.io/ecma262/#sec-array.prototype.lastindexof
module.exports = FORCED ? function lastIndexOf(searchElement /* , fromIndex = @[*-1] */) {
  // convert -0 to +0
  if (NEGATIVE_ZERO) return nativeLastIndexOf.apply(this, arguments) || 0;
  var O = toIndexedObject(this);
  var length = toLength(O.length);
  var index = length - 1;
  if (arguments.length > 1) index = min(index, toInteger(arguments[1]));
  if (index < 0) index = length + index;
  for (;index >= 0; index--) if (index in O && O[index] === searchElement) return index || 0;
  return -1;
} : nativeLastIndexOf;


/***/ }),
/* 340 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var DOMIterables = __webpack_require__(239);
var forEach = __webpack_require__(212);
var createNonEnumerableProperty = __webpack_require__(18);

for (var COLLECTION_NAME in DOMIterables) {
  var Collection = global[COLLECTION_NAME];
  var CollectionPrototype = Collection && Collection.prototype;
  // some Chrome versions have non-configurable methods on DOMTokenList
  if (CollectionPrototype && CollectionPrototype.forEach !== forEach) try {
    createNonEnumerableProperty(CollectionPrototype, 'forEach', forEach);
  } catch (error) {
    CollectionPrototype.forEach = forEach;
  }
}


/***/ }),
/* 341 */
/***/ (function(module, exports) {

/* (ignored) */

/***/ }),
/* 342 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

var Buffer = __webpack_require__(124).Buffer;
var util = __webpack_require__(343);

function copyBuffer(src, target, offset) {
  src.copy(target, offset);
}

module.exports = function () {
  function BufferList() {
    _classCallCheck(this, BufferList);

    this.head = null;
    this.tail = null;
    this.length = 0;
  }

  BufferList.prototype.push = function push(v) {
    var entry = { data: v, next: null };
    if (this.length > 0) this.tail.next = entry;else this.head = entry;
    this.tail = entry;
    ++this.length;
  };

  BufferList.prototype.unshift = function unshift(v) {
    var entry = { data: v, next: this.head };
    if (this.length === 0) this.tail = entry;
    this.head = entry;
    ++this.length;
  };

  BufferList.prototype.shift = function shift() {
    if (this.length === 0) return;
    var ret = this.head.data;
    if (this.length === 1) this.head = this.tail = null;else this.head = this.head.next;
    --this.length;
    return ret;
  };

  BufferList.prototype.clear = function clear() {
    this.head = this.tail = null;
    this.length = 0;
  };

  BufferList.prototype.join = function join(s) {
    if (this.length === 0) return '';
    var p = this.head;
    var ret = '' + p.data;
    while (p = p.next) {
      ret += s + p.data;
    }return ret;
  };

  BufferList.prototype.concat = function concat(n) {
    if (this.length === 0) return Buffer.alloc(0);
    if (this.length === 1) return this.head.data;
    var ret = Buffer.allocUnsafe(n >>> 0);
    var p = this.head;
    var i = 0;
    while (p) {
      copyBuffer(p.data, ret, i);
      i += p.data.length;
      p = p.next;
    }
    return ret;
  };

  return BufferList;
}();

if (util && util.inspect && util.inspect.custom) {
  module.exports.prototype[util.inspect.custom] = function () {
    var obj = util.inspect({ length: this.length });
    return this.constructor.name + ' ' + obj;
  };
}

/***/ }),
/* 343 */
/***/ (function(module, exports) {

/* (ignored) */

/***/ }),
/* 344 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(global) {
/**
 * Module exports.
 */

module.exports = deprecate;

/**
 * Mark that a method should not be used.
 * Returns a modified function which warns once by default.
 *
 * If `localStorage.noDeprecation = true` is set, then it is a no-op.
 *
 * If `localStorage.throwDeprecation = true` is set, then deprecated functions
 * will throw an Error when invoked.
 *
 * If `localStorage.traceDeprecation = true` is set, then deprecated functions
 * will invoke `console.trace()` instead of `console.error()`.
 *
 * @param {Function} fn - the function to deprecate
 * @param {String} msg - the string to print to the console when `fn` is invoked
 * @returns {Function} a new "deprecated" version of `fn`
 * @api public
 */

function deprecate (fn, msg) {
  if (config('noDeprecation')) {
    return fn;
  }

  var warned = false;
  function deprecated() {
    if (!warned) {
      if (config('throwDeprecation')) {
        throw new Error(msg);
      } else if (config('traceDeprecation')) {
        console.trace(msg);
      } else {
        console.warn(msg);
      }
      warned = true;
    }
    return fn.apply(this, arguments);
  }

  return deprecated;
}

/**
 * Checks `localStorage` for boolean values for the given `name`.
 *
 * @param {String} name
 * @returns {Boolean}
 * @api private
 */

function config (name) {
  // accessing global.localStorage can trigger a DOMException in sandboxed iframes
  try {
    if (!global.localStorage) return false;
  } catch (_) {
    return false;
  }
  var val = global.localStorage[name];
  if (null == val) return false;
  return String(val).toLowerCase() === 'true';
}

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(24)))

/***/ }),
/* 345 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

// a passthrough stream.
// basically just the most minimal sort of Transform stream.
// Every written chunk gets output as-is.



module.exports = PassThrough;

var Transform = __webpack_require__(243);

/*<replacement>*/
var util = Object.create(__webpack_require__(98));
util.inherits = __webpack_require__(86);
/*</replacement>*/

util.inherits(PassThrough, Transform);

function PassThrough(options) {
  if (!(this instanceof PassThrough)) return new PassThrough(options);

  Transform.call(this, options);
}

PassThrough.prototype._transform = function (chunk, encoding, cb) {
  cb(null, chunk);
};

/***/ }),
/* 346 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = __webpack_require__(165);


/***/ }),
/* 347 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = __webpack_require__(48);


/***/ }),
/* 348 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = __webpack_require__(164).Transform


/***/ }),
/* 349 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = __webpack_require__(164).PassThrough


/***/ }),
/* 350 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(Buffer, process) {
/* eslint camelcase: "off" */

var assert = __webpack_require__(245);

var Zstream = __webpack_require__(354);
var zlib_deflate = __webpack_require__(355);
var zlib_inflate = __webpack_require__(358);
var constants = __webpack_require__(361);

for (var key in constants) {
  exports[key] = constants[key];
}

// zlib modes
exports.NONE = 0;
exports.DEFLATE = 1;
exports.INFLATE = 2;
exports.GZIP = 3;
exports.GUNZIP = 4;
exports.DEFLATERAW = 5;
exports.INFLATERAW = 6;
exports.UNZIP = 7;

var GZIP_HEADER_ID1 = 0x1f;
var GZIP_HEADER_ID2 = 0x8b;

/**
 * Emulate Node's zlib C++ layer for use by the JS layer in index.js
 */
function Zlib(mode) {
  if (typeof mode !== 'number' || mode < exports.DEFLATE || mode > exports.UNZIP) {
    throw new TypeError('Bad argument');
  }

  this.dictionary = null;
  this.err = 0;
  this.flush = 0;
  this.init_done = false;
  this.level = 0;
  this.memLevel = 0;
  this.mode = mode;
  this.strategy = 0;
  this.windowBits = 0;
  this.write_in_progress = false;
  this.pending_close = false;
  this.gzip_id_bytes_read = 0;
}

Zlib.prototype.close = function () {
  if (this.write_in_progress) {
    this.pending_close = true;
    return;
  }

  this.pending_close = false;

  assert(this.init_done, 'close before init');
  assert(this.mode <= exports.UNZIP);

  if (this.mode === exports.DEFLATE || this.mode === exports.GZIP || this.mode === exports.DEFLATERAW) {
    zlib_deflate.deflateEnd(this.strm);
  } else if (this.mode === exports.INFLATE || this.mode === exports.GUNZIP || this.mode === exports.INFLATERAW || this.mode === exports.UNZIP) {
    zlib_inflate.inflateEnd(this.strm);
  }

  this.mode = exports.NONE;

  this.dictionary = null;
};

Zlib.prototype.write = function (flush, input, in_off, in_len, out, out_off, out_len) {
  return this._write(true, flush, input, in_off, in_len, out, out_off, out_len);
};

Zlib.prototype.writeSync = function (flush, input, in_off, in_len, out, out_off, out_len) {
  return this._write(false, flush, input, in_off, in_len, out, out_off, out_len);
};

Zlib.prototype._write = function (async, flush, input, in_off, in_len, out, out_off, out_len) {
  assert.equal(arguments.length, 8);

  assert(this.init_done, 'write before init');
  assert(this.mode !== exports.NONE, 'already finalized');
  assert.equal(false, this.write_in_progress, 'write already in progress');
  assert.equal(false, this.pending_close, 'close is pending');

  this.write_in_progress = true;

  assert.equal(false, flush === undefined, 'must provide flush value');

  this.write_in_progress = true;

  if (flush !== exports.Z_NO_FLUSH && flush !== exports.Z_PARTIAL_FLUSH && flush !== exports.Z_SYNC_FLUSH && flush !== exports.Z_FULL_FLUSH && flush !== exports.Z_FINISH && flush !== exports.Z_BLOCK) {
    throw new Error('Invalid flush value');
  }

  if (input == null) {
    input = Buffer.alloc(0);
    in_len = 0;
    in_off = 0;
  }

  this.strm.avail_in = in_len;
  this.strm.input = input;
  this.strm.next_in = in_off;
  this.strm.avail_out = out_len;
  this.strm.output = out;
  this.strm.next_out = out_off;
  this.flush = flush;

  if (!async) {
    // sync version
    this._process();

    if (this._checkError()) {
      return this._afterSync();
    }
    return;
  }

  // async version
  var self = this;
  process.nextTick(function () {
    self._process();
    self._after();
  });

  return this;
};

Zlib.prototype._afterSync = function () {
  var avail_out = this.strm.avail_out;
  var avail_in = this.strm.avail_in;

  this.write_in_progress = false;

  return [avail_in, avail_out];
};

Zlib.prototype._process = function () {
  var next_expected_header_byte = null;

  // If the avail_out is left at 0, then it means that it ran out
  // of room.  If there was avail_out left over, then it means
  // that all of the input was consumed.
  switch (this.mode) {
    case exports.DEFLATE:
    case exports.GZIP:
    case exports.DEFLATERAW:
      this.err = zlib_deflate.deflate(this.strm, this.flush);
      break;
    case exports.UNZIP:
      if (this.strm.avail_in > 0) {
        next_expected_header_byte = this.strm.next_in;
      }

      switch (this.gzip_id_bytes_read) {
        case 0:
          if (next_expected_header_byte === null) {
            break;
          }

          if (this.strm.input[next_expected_header_byte] === GZIP_HEADER_ID1) {
            this.gzip_id_bytes_read = 1;
            next_expected_header_byte++;

            if (this.strm.avail_in === 1) {
              // The only available byte was already read.
              break;
            }
          } else {
            this.mode = exports.INFLATE;
            break;
          }

        // fallthrough
        case 1:
          if (next_expected_header_byte === null) {
            break;
          }

          if (this.strm.input[next_expected_header_byte] === GZIP_HEADER_ID2) {
            this.gzip_id_bytes_read = 2;
            this.mode = exports.GUNZIP;
          } else {
            // There is no actual difference between INFLATE and INFLATERAW
            // (after initialization).
            this.mode = exports.INFLATE;
          }

          break;
        default:
          throw new Error('invalid number of gzip magic number bytes read');
      }

    // fallthrough
    case exports.INFLATE:
    case exports.GUNZIP:
    case exports.INFLATERAW:
      this.err = zlib_inflate.inflate(this.strm, this.flush

      // If data was encoded with dictionary
      );if (this.err === exports.Z_NEED_DICT && this.dictionary) {
        // Load it
        this.err = zlib_inflate.inflateSetDictionary(this.strm, this.dictionary);
        if (this.err === exports.Z_OK) {
          // And try to decode again
          this.err = zlib_inflate.inflate(this.strm, this.flush);
        } else if (this.err === exports.Z_DATA_ERROR) {
          // Both inflateSetDictionary() and inflate() return Z_DATA_ERROR.
          // Make it possible for After() to tell a bad dictionary from bad
          // input.
          this.err = exports.Z_NEED_DICT;
        }
      }
      while (this.strm.avail_in > 0 && this.mode === exports.GUNZIP && this.err === exports.Z_STREAM_END && this.strm.next_in[0] !== 0x00) {
        // Bytes remain in input buffer. Perhaps this is another compressed
        // member in the same archive, or just trailing garbage.
        // Trailing zero bytes are okay, though, since they are frequently
        // used for padding.

        this.reset();
        this.err = zlib_inflate.inflate(this.strm, this.flush);
      }
      break;
    default:
      throw new Error('Unknown mode ' + this.mode);
  }
};

Zlib.prototype._checkError = function () {
  // Acceptable error states depend on the type of zlib stream.
  switch (this.err) {
    case exports.Z_OK:
    case exports.Z_BUF_ERROR:
      if (this.strm.avail_out !== 0 && this.flush === exports.Z_FINISH) {
        this._error('unexpected end of file');
        return false;
      }
      break;
    case exports.Z_STREAM_END:
      // normal statuses, not fatal
      break;
    case exports.Z_NEED_DICT:
      if (this.dictionary == null) {
        this._error('Missing dictionary');
      } else {
        this._error('Bad dictionary');
      }
      return false;
    default:
      // something else.
      this._error('Zlib error');
      return false;
  }

  return true;
};

Zlib.prototype._after = function () {
  if (!this._checkError()) {
    return;
  }

  var avail_out = this.strm.avail_out;
  var avail_in = this.strm.avail_in;

  this.write_in_progress = false;

  // call the write() cb
  this.callback(avail_in, avail_out);

  if (this.pending_close) {
    this.close();
  }
};

Zlib.prototype._error = function (message) {
  if (this.strm.msg) {
    message = this.strm.msg;
  }
  this.onerror(message, this.err

  // no hope of rescue.
  );this.write_in_progress = false;
  if (this.pending_close) {
    this.close();
  }
};

Zlib.prototype.init = function (windowBits, level, memLevel, strategy, dictionary) {
  assert(arguments.length === 4 || arguments.length === 5, 'init(windowBits, level, memLevel, strategy, [dictionary])');

  assert(windowBits >= 8 && windowBits <= 15, 'invalid windowBits');
  assert(level >= -1 && level <= 9, 'invalid compression level');

  assert(memLevel >= 1 && memLevel <= 9, 'invalid memlevel');

  assert(strategy === exports.Z_FILTERED || strategy === exports.Z_HUFFMAN_ONLY || strategy === exports.Z_RLE || strategy === exports.Z_FIXED || strategy === exports.Z_DEFAULT_STRATEGY, 'invalid strategy');

  this._init(level, windowBits, memLevel, strategy, dictionary);
  this._setDictionary();
};

Zlib.prototype.params = function () {
  throw new Error('deflateParams Not supported');
};

Zlib.prototype.reset = function () {
  this._reset();
  this._setDictionary();
};

Zlib.prototype._init = function (level, windowBits, memLevel, strategy, dictionary) {
  this.level = level;
  this.windowBits = windowBits;
  this.memLevel = memLevel;
  this.strategy = strategy;

  this.flush = exports.Z_NO_FLUSH;

  this.err = exports.Z_OK;

  if (this.mode === exports.GZIP || this.mode === exports.GUNZIP) {
    this.windowBits += 16;
  }

  if (this.mode === exports.UNZIP) {
    this.windowBits += 32;
  }

  if (this.mode === exports.DEFLATERAW || this.mode === exports.INFLATERAW) {
    this.windowBits = -1 * this.windowBits;
  }

  this.strm = new Zstream();

  switch (this.mode) {
    case exports.DEFLATE:
    case exports.GZIP:
    case exports.DEFLATERAW:
      this.err = zlib_deflate.deflateInit2(this.strm, this.level, exports.Z_DEFLATED, this.windowBits, this.memLevel, this.strategy);
      break;
    case exports.INFLATE:
    case exports.GUNZIP:
    case exports.INFLATERAW:
    case exports.UNZIP:
      this.err = zlib_inflate.inflateInit2(this.strm, this.windowBits);
      break;
    default:
      throw new Error('Unknown mode ' + this.mode);
  }

  if (this.err !== exports.Z_OK) {
    this._error('Init error');
  }

  this.dictionary = dictionary;

  this.write_in_progress = false;
  this.init_done = true;
};

Zlib.prototype._setDictionary = function () {
  if (this.dictionary == null) {
    return;
  }

  this.err = exports.Z_OK;

  switch (this.mode) {
    case exports.DEFLATE:
    case exports.DEFLATERAW:
      this.err = zlib_deflate.deflateSetDictionary(this.strm, this.dictionary);
      break;
    default:
      break;
  }

  if (this.err !== exports.Z_OK) {
    this._error('Failed to set dictionary');
  }
};

Zlib.prototype._reset = function () {
  this.err = exports.Z_OK;

  switch (this.mode) {
    case exports.DEFLATE:
    case exports.DEFLATERAW:
    case exports.GZIP:
      this.err = zlib_deflate.deflateReset(this.strm);
      break;
    case exports.INFLATE:
    case exports.INFLATERAW:
    case exports.GUNZIP:
      this.err = zlib_inflate.inflateReset(this.strm);
      break;
    default:
      break;
  }

  if (this.err !== exports.Z_OK) {
    this._error('Failed to reset stream');
  }
};

exports.Zlib = Zlib;
/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer, __webpack_require__(47)))

/***/ }),
/* 351 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/*
object-assign
(c) Sindre Sorhus
@license MIT
*/


/* eslint-disable no-unused-vars */
var getOwnPropertySymbols = Object.getOwnPropertySymbols;
var hasOwnProperty = Object.prototype.hasOwnProperty;
var propIsEnumerable = Object.prototype.propertyIsEnumerable;

function toObject(val) {
	if (val === null || val === undefined) {
		throw new TypeError('Object.assign cannot be called with null or undefined');
	}

	return Object(val);
}

function shouldUseNative() {
	try {
		if (!Object.assign) {
			return false;
		}

		// Detect buggy property enumeration order in older V8 versions.

		// https://bugs.chromium.org/p/v8/issues/detail?id=4118
		var test1 = new String('abc');  // eslint-disable-line no-new-wrappers
		test1[5] = 'de';
		if (Object.getOwnPropertyNames(test1)[0] === '5') {
			return false;
		}

		// https://bugs.chromium.org/p/v8/issues/detail?id=3056
		var test2 = {};
		for (var i = 0; i < 10; i++) {
			test2['_' + String.fromCharCode(i)] = i;
		}
		var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
			return test2[n];
		});
		if (order2.join('') !== '0123456789') {
			return false;
		}

		// https://bugs.chromium.org/p/v8/issues/detail?id=3056
		var test3 = {};
		'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
			test3[letter] = letter;
		});
		if (Object.keys(Object.assign({}, test3)).join('') !==
				'abcdefghijklmnopqrst') {
			return false;
		}

		return true;
	} catch (err) {
		// We don't expect any of the above to throw, but better to be safe.
		return false;
	}
}

module.exports = shouldUseNative() ? Object.assign : function (target, source) {
	var from;
	var to = toObject(target);
	var symbols;

	for (var s = 1; s < arguments.length; s++) {
		from = Object(arguments[s]);

		for (var key in from) {
			if (hasOwnProperty.call(from, key)) {
				to[key] = from[key];
			}
		}

		if (getOwnPropertySymbols) {
			symbols = getOwnPropertySymbols(from);
			for (var i = 0; i < symbols.length; i++) {
				if (propIsEnumerable.call(from, symbols[i])) {
					to[symbols[i]] = from[symbols[i]];
				}
			}
		}
	}

	return to;
};


/***/ }),
/* 352 */
/***/ (function(module, exports) {

module.exports = function isBuffer(arg) {
  return arg && typeof arg === 'object'
    && typeof arg.copy === 'function'
    && typeof arg.fill === 'function'
    && typeof arg.readUInt8 === 'function';
}

/***/ }),
/* 353 */
/***/ (function(module, exports) {

if (typeof Object.create === 'function') {
  // implementation from standard node.js 'util' module
  module.exports = function inherits(ctor, superCtor) {
    ctor.super_ = superCtor
    ctor.prototype = Object.create(superCtor.prototype, {
      constructor: {
        value: ctor,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
  };
} else {
  // old school shim for old browsers
  module.exports = function inherits(ctor, superCtor) {
    ctor.super_ = superCtor
    var TempCtor = function () {}
    TempCtor.prototype = superCtor.prototype
    ctor.prototype = new TempCtor()
    ctor.prototype.constructor = ctor
  }
}


/***/ }),
/* 354 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

function ZStream() {
  /* next input byte */
  this.input = null; // JS specific, because we have no pointers
  this.next_in = 0;
  /* number of bytes available at input */
  this.avail_in = 0;
  /* total number of input bytes read so far */
  this.total_in = 0;
  /* next output byte should be put there */
  this.output = null; // JS specific, because we have no pointers
  this.next_out = 0;
  /* remaining free space at output */
  this.avail_out = 0;
  /* total number of bytes output so far */
  this.total_out = 0;
  /* last error message, NULL if no error */
  this.msg = ''/*Z_NULL*/;
  /* not visible by applications */
  this.state = null;
  /* best guess about the data type: binary or text */
  this.data_type = 2/*Z_UNKNOWN*/;
  /* adler32 value of the uncompressed data */
  this.adler = 0;
}

module.exports = ZStream;


/***/ }),
/* 355 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

var utils   = __webpack_require__(126);
var trees   = __webpack_require__(356);
var adler32 = __webpack_require__(246);
var crc32   = __webpack_require__(247);
var msg     = __webpack_require__(357);

/* Public constants ==========================================================*/
/* ===========================================================================*/


/* Allowed flush values; see deflate() and inflate() below for details */
var Z_NO_FLUSH      = 0;
var Z_PARTIAL_FLUSH = 1;
//var Z_SYNC_FLUSH    = 2;
var Z_FULL_FLUSH    = 3;
var Z_FINISH        = 4;
var Z_BLOCK         = 5;
//var Z_TREES         = 6;


/* Return codes for the compression/decompression functions. Negative values
 * are errors, positive values are used for special but normal events.
 */
var Z_OK            = 0;
var Z_STREAM_END    = 1;
//var Z_NEED_DICT     = 2;
//var Z_ERRNO         = -1;
var Z_STREAM_ERROR  = -2;
var Z_DATA_ERROR    = -3;
//var Z_MEM_ERROR     = -4;
var Z_BUF_ERROR     = -5;
//var Z_VERSION_ERROR = -6;


/* compression levels */
//var Z_NO_COMPRESSION      = 0;
//var Z_BEST_SPEED          = 1;
//var Z_BEST_COMPRESSION    = 9;
var Z_DEFAULT_COMPRESSION = -1;


var Z_FILTERED            = 1;
var Z_HUFFMAN_ONLY        = 2;
var Z_RLE                 = 3;
var Z_FIXED               = 4;
var Z_DEFAULT_STRATEGY    = 0;

/* Possible values of the data_type field (though see inflate()) */
//var Z_BINARY              = 0;
//var Z_TEXT                = 1;
//var Z_ASCII               = 1; // = Z_TEXT
var Z_UNKNOWN             = 2;


/* The deflate compression method */
var Z_DEFLATED  = 8;

/*============================================================================*/


var MAX_MEM_LEVEL = 9;
/* Maximum value for memLevel in deflateInit2 */
var MAX_WBITS = 15;
/* 32K LZ77 window */
var DEF_MEM_LEVEL = 8;


var LENGTH_CODES  = 29;
/* number of length codes, not counting the special END_BLOCK code */
var LITERALS      = 256;
/* number of literal bytes 0..255 */
var L_CODES       = LITERALS + 1 + LENGTH_CODES;
/* number of Literal or Length codes, including the END_BLOCK code */
var D_CODES       = 30;
/* number of distance codes */
var BL_CODES      = 19;
/* number of codes used to transfer the bit lengths */
var HEAP_SIZE     = 2 * L_CODES + 1;
/* maximum heap size */
var MAX_BITS  = 15;
/* All codes must not exceed MAX_BITS bits */

var MIN_MATCH = 3;
var MAX_MATCH = 258;
var MIN_LOOKAHEAD = (MAX_MATCH + MIN_MATCH + 1);

var PRESET_DICT = 0x20;

var INIT_STATE = 42;
var EXTRA_STATE = 69;
var NAME_STATE = 73;
var COMMENT_STATE = 91;
var HCRC_STATE = 103;
var BUSY_STATE = 113;
var FINISH_STATE = 666;

var BS_NEED_MORE      = 1; /* block not completed, need more input or more output */
var BS_BLOCK_DONE     = 2; /* block flush performed */
var BS_FINISH_STARTED = 3; /* finish started, need only more output at next deflate */
var BS_FINISH_DONE    = 4; /* finish done, accept no more input or output */

var OS_CODE = 0x03; // Unix :) . Don't detect, use this default.

function err(strm, errorCode) {
  strm.msg = msg[errorCode];
  return errorCode;
}

function rank(f) {
  return ((f) << 1) - ((f) > 4 ? 9 : 0);
}

function zero(buf) { var len = buf.length; while (--len >= 0) { buf[len] = 0; } }


/* =========================================================================
 * Flush as much pending output as possible. All deflate() output goes
 * through this function so some applications may wish to modify it
 * to avoid allocating a large strm->output buffer and copying into it.
 * (See also read_buf()).
 */
function flush_pending(strm) {
  var s = strm.state;

  //_tr_flush_bits(s);
  var len = s.pending;
  if (len > strm.avail_out) {
    len = strm.avail_out;
  }
  if (len === 0) { return; }

  utils.arraySet(strm.output, s.pending_buf, s.pending_out, len, strm.next_out);
  strm.next_out += len;
  s.pending_out += len;
  strm.total_out += len;
  strm.avail_out -= len;
  s.pending -= len;
  if (s.pending === 0) {
    s.pending_out = 0;
  }
}


function flush_block_only(s, last) {
  trees._tr_flush_block(s, (s.block_start >= 0 ? s.block_start : -1), s.strstart - s.block_start, last);
  s.block_start = s.strstart;
  flush_pending(s.strm);
}


function put_byte(s, b) {
  s.pending_buf[s.pending++] = b;
}


/* =========================================================================
 * Put a short in the pending buffer. The 16-bit value is put in MSB order.
 * IN assertion: the stream state is correct and there is enough room in
 * pending_buf.
 */
function putShortMSB(s, b) {
//  put_byte(s, (Byte)(b >> 8));
//  put_byte(s, (Byte)(b & 0xff));
  s.pending_buf[s.pending++] = (b >>> 8) & 0xff;
  s.pending_buf[s.pending++] = b & 0xff;
}


/* ===========================================================================
 * Read a new buffer from the current input stream, update the adler32
 * and total number of bytes read.  All deflate() input goes through
 * this function so some applications may wish to modify it to avoid
 * allocating a large strm->input buffer and copying from it.
 * (See also flush_pending()).
 */
function read_buf(strm, buf, start, size) {
  var len = strm.avail_in;

  if (len > size) { len = size; }
  if (len === 0) { return 0; }

  strm.avail_in -= len;

  // zmemcpy(buf, strm->next_in, len);
  utils.arraySet(buf, strm.input, strm.next_in, len, start);
  if (strm.state.wrap === 1) {
    strm.adler = adler32(strm.adler, buf, len, start);
  }

  else if (strm.state.wrap === 2) {
    strm.adler = crc32(strm.adler, buf, len, start);
  }

  strm.next_in += len;
  strm.total_in += len;

  return len;
}


/* ===========================================================================
 * Set match_start to the longest match starting at the given string and
 * return its length. Matches shorter or equal to prev_length are discarded,
 * in which case the result is equal to prev_length and match_start is
 * garbage.
 * IN assertions: cur_match is the head of the hash chain for the current
 *   string (strstart) and its distance is <= MAX_DIST, and prev_length >= 1
 * OUT assertion: the match length is not greater than s->lookahead.
 */
function longest_match(s, cur_match) {
  var chain_length = s.max_chain_length;      /* max hash chain length */
  var scan = s.strstart; /* current string */
  var match;                       /* matched string */
  var len;                           /* length of current match */
  var best_len = s.prev_length;              /* best match length so far */
  var nice_match = s.nice_match;             /* stop if match long enough */
  var limit = (s.strstart > (s.w_size - MIN_LOOKAHEAD)) ?
      s.strstart - (s.w_size - MIN_LOOKAHEAD) : 0/*NIL*/;

  var _win = s.window; // shortcut

  var wmask = s.w_mask;
  var prev  = s.prev;

  /* Stop when cur_match becomes <= limit. To simplify the code,
   * we prevent matches with the string of window index 0.
   */

  var strend = s.strstart + MAX_MATCH;
  var scan_end1  = _win[scan + best_len - 1];
  var scan_end   = _win[scan + best_len];

  /* The code is optimized for HASH_BITS >= 8 and MAX_MATCH-2 multiple of 16.
   * It is easy to get rid of this optimization if necessary.
   */
  // Assert(s->hash_bits >= 8 && MAX_MATCH == 258, "Code too clever");

  /* Do not waste too much time if we already have a good match: */
  if (s.prev_length >= s.good_match) {
    chain_length >>= 2;
  }
  /* Do not look for matches beyond the end of the input. This is necessary
   * to make deflate deterministic.
   */
  if (nice_match > s.lookahead) { nice_match = s.lookahead; }

  // Assert((ulg)s->strstart <= s->window_size-MIN_LOOKAHEAD, "need lookahead");

  do {
    // Assert(cur_match < s->strstart, "no future");
    match = cur_match;

    /* Skip to next match if the match length cannot increase
     * or if the match length is less than 2.  Note that the checks below
     * for insufficient lookahead only occur occasionally for performance
     * reasons.  Therefore uninitialized memory will be accessed, and
     * conditional jumps will be made that depend on those values.
     * However the length of the match is limited to the lookahead, so
     * the output of deflate is not affected by the uninitialized values.
     */

    if (_win[match + best_len]     !== scan_end  ||
        _win[match + best_len - 1] !== scan_end1 ||
        _win[match]                !== _win[scan] ||
        _win[++match]              !== _win[scan + 1]) {
      continue;
    }

    /* The check at best_len-1 can be removed because it will be made
     * again later. (This heuristic is not always a win.)
     * It is not necessary to compare scan[2] and match[2] since they
     * are always equal when the other bytes match, given that
     * the hash keys are equal and that HASH_BITS >= 8.
     */
    scan += 2;
    match++;
    // Assert(*scan == *match, "match[2]?");

    /* We check for insufficient lookahead only every 8th comparison;
     * the 256th check will be made at strstart+258.
     */
    do {
      /*jshint noempty:false*/
    } while (_win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             _win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             _win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             _win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             scan < strend);

    // Assert(scan <= s->window+(unsigned)(s->window_size-1), "wild scan");

    len = MAX_MATCH - (strend - scan);
    scan = strend - MAX_MATCH;

    if (len > best_len) {
      s.match_start = cur_match;
      best_len = len;
      if (len >= nice_match) {
        break;
      }
      scan_end1  = _win[scan + best_len - 1];
      scan_end   = _win[scan + best_len];
    }
  } while ((cur_match = prev[cur_match & wmask]) > limit && --chain_length !== 0);

  if (best_len <= s.lookahead) {
    return best_len;
  }
  return s.lookahead;
}


/* ===========================================================================
 * Fill the window when the lookahead becomes insufficient.
 * Updates strstart and lookahead.
 *
 * IN assertion: lookahead < MIN_LOOKAHEAD
 * OUT assertions: strstart <= window_size-MIN_LOOKAHEAD
 *    At least one byte has been read, or avail_in == 0; reads are
 *    performed for at least two bytes (required for the zip translate_eol
 *    option -- not supported here).
 */
function fill_window(s) {
  var _w_size = s.w_size;
  var p, n, m, more, str;

  //Assert(s->lookahead < MIN_LOOKAHEAD, "already enough lookahead");

  do {
    more = s.window_size - s.lookahead - s.strstart;

    // JS ints have 32 bit, block below not needed
    /* Deal with !@#$% 64K limit: */
    //if (sizeof(int) <= 2) {
    //    if (more == 0 && s->strstart == 0 && s->lookahead == 0) {
    //        more = wsize;
    //
    //  } else if (more == (unsigned)(-1)) {
    //        /* Very unlikely, but possible on 16 bit machine if
    //         * strstart == 0 && lookahead == 1 (input done a byte at time)
    //         */
    //        more--;
    //    }
    //}


    /* If the window is almost full and there is insufficient lookahead,
     * move the upper half to the lower one to make room in the upper half.
     */
    if (s.strstart >= _w_size + (_w_size - MIN_LOOKAHEAD)) {

      utils.arraySet(s.window, s.window, _w_size, _w_size, 0);
      s.match_start -= _w_size;
      s.strstart -= _w_size;
      /* we now have strstart >= MAX_DIST */
      s.block_start -= _w_size;

      /* Slide the hash table (could be avoided with 32 bit values
       at the expense of memory usage). We slide even when level == 0
       to keep the hash table consistent if we switch back to level > 0
       later. (Using level 0 permanently is not an optimal usage of
       zlib, so we don't care about this pathological case.)
       */

      n = s.hash_size;
      p = n;
      do {
        m = s.head[--p];
        s.head[p] = (m >= _w_size ? m - _w_size : 0);
      } while (--n);

      n = _w_size;
      p = n;
      do {
        m = s.prev[--p];
        s.prev[p] = (m >= _w_size ? m - _w_size : 0);
        /* If n is not on any hash chain, prev[n] is garbage but
         * its value will never be used.
         */
      } while (--n);

      more += _w_size;
    }
    if (s.strm.avail_in === 0) {
      break;
    }

    /* If there was no sliding:
     *    strstart <= WSIZE+MAX_DIST-1 && lookahead <= MIN_LOOKAHEAD - 1 &&
     *    more == window_size - lookahead - strstart
     * => more >= window_size - (MIN_LOOKAHEAD-1 + WSIZE + MAX_DIST-1)
     * => more >= window_size - 2*WSIZE + 2
     * In the BIG_MEM or MMAP case (not yet supported),
     *   window_size == input_size + MIN_LOOKAHEAD  &&
     *   strstart + s->lookahead <= input_size => more >= MIN_LOOKAHEAD.
     * Otherwise, window_size == 2*WSIZE so more >= 2.
     * If there was sliding, more >= WSIZE. So in all cases, more >= 2.
     */
    //Assert(more >= 2, "more < 2");
    n = read_buf(s.strm, s.window, s.strstart + s.lookahead, more);
    s.lookahead += n;

    /* Initialize the hash value now that we have some input: */
    if (s.lookahead + s.insert >= MIN_MATCH) {
      str = s.strstart - s.insert;
      s.ins_h = s.window[str];

      /* UPDATE_HASH(s, s->ins_h, s->window[str + 1]); */
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[str + 1]) & s.hash_mask;
//#if MIN_MATCH != 3
//        Call update_hash() MIN_MATCH-3 more times
//#endif
      while (s.insert) {
        /* UPDATE_HASH(s, s->ins_h, s->window[str + MIN_MATCH-1]); */
        s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[str + MIN_MATCH - 1]) & s.hash_mask;

        s.prev[str & s.w_mask] = s.head[s.ins_h];
        s.head[s.ins_h] = str;
        str++;
        s.insert--;
        if (s.lookahead + s.insert < MIN_MATCH) {
          break;
        }
      }
    }
    /* If the whole input has less than MIN_MATCH bytes, ins_h is garbage,
     * but this is not important since only literal bytes will be emitted.
     */

  } while (s.lookahead < MIN_LOOKAHEAD && s.strm.avail_in !== 0);

  /* If the WIN_INIT bytes after the end of the current data have never been
   * written, then zero those bytes in order to avoid memory check reports of
   * the use of uninitialized (or uninitialised as Julian writes) bytes by
   * the longest match routines.  Update the high water mark for the next
   * time through here.  WIN_INIT is set to MAX_MATCH since the longest match
   * routines allow scanning to strstart + MAX_MATCH, ignoring lookahead.
   */
//  if (s.high_water < s.window_size) {
//    var curr = s.strstart + s.lookahead;
//    var init = 0;
//
//    if (s.high_water < curr) {
//      /* Previous high water mark below current data -- zero WIN_INIT
//       * bytes or up to end of window, whichever is less.
//       */
//      init = s.window_size - curr;
//      if (init > WIN_INIT)
//        init = WIN_INIT;
//      zmemzero(s->window + curr, (unsigned)init);
//      s->high_water = curr + init;
//    }
//    else if (s->high_water < (ulg)curr + WIN_INIT) {
//      /* High water mark at or above current data, but below current data
//       * plus WIN_INIT -- zero out to current data plus WIN_INIT, or up
//       * to end of window, whichever is less.
//       */
//      init = (ulg)curr + WIN_INIT - s->high_water;
//      if (init > s->window_size - s->high_water)
//        init = s->window_size - s->high_water;
//      zmemzero(s->window + s->high_water, (unsigned)init);
//      s->high_water += init;
//    }
//  }
//
//  Assert((ulg)s->strstart <= s->window_size - MIN_LOOKAHEAD,
//    "not enough room for search");
}

/* ===========================================================================
 * Copy without compression as much as possible from the input stream, return
 * the current block state.
 * This function does not insert new strings in the dictionary since
 * uncompressible data is probably not useful. This function is used
 * only for the level=0 compression option.
 * NOTE: this function should be optimized to avoid extra copying from
 * window to pending_buf.
 */
function deflate_stored(s, flush) {
  /* Stored blocks are limited to 0xffff bytes, pending_buf is limited
   * to pending_buf_size, and each stored block has a 5 byte header:
   */
  var max_block_size = 0xffff;

  if (max_block_size > s.pending_buf_size - 5) {
    max_block_size = s.pending_buf_size - 5;
  }

  /* Copy as much as possible from input to output: */
  for (;;) {
    /* Fill the window as much as possible: */
    if (s.lookahead <= 1) {

      //Assert(s->strstart < s->w_size+MAX_DIST(s) ||
      //  s->block_start >= (long)s->w_size, "slide too late");
//      if (!(s.strstart < s.w_size + (s.w_size - MIN_LOOKAHEAD) ||
//        s.block_start >= s.w_size)) {
//        throw  new Error("slide too late");
//      }

      fill_window(s);
      if (s.lookahead === 0 && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }

      if (s.lookahead === 0) {
        break;
      }
      /* flush the current block */
    }
    //Assert(s->block_start >= 0L, "block gone");
//    if (s.block_start < 0) throw new Error("block gone");

    s.strstart += s.lookahead;
    s.lookahead = 0;

    /* Emit a stored block if pending_buf will be full: */
    var max_start = s.block_start + max_block_size;

    if (s.strstart === 0 || s.strstart >= max_start) {
      /* strstart == 0 is possible when wraparound on 16-bit machine */
      s.lookahead = s.strstart - max_start;
      s.strstart = max_start;
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/


    }
    /* Flush if we may have to slide, otherwise block_start may become
     * negative and the data will be gone:
     */
    if (s.strstart - s.block_start >= (s.w_size - MIN_LOOKAHEAD)) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }

  s.insert = 0;

  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }

  if (s.strstart > s.block_start) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }

  return BS_NEED_MORE;
}

/* ===========================================================================
 * Compress as much as possible from the input stream, return the current
 * block state.
 * This function does not perform lazy evaluation of matches and inserts
 * new strings in the dictionary only for unmatched strings or for short
 * matches. It is used only for the fast compression options.
 */
function deflate_fast(s, flush) {
  var hash_head;        /* head of the hash chain */
  var bflush;           /* set if current block must be flushed */

  for (;;) {
    /* Make sure that we always have enough lookahead, except
     * at the end of the input file. We need MAX_MATCH bytes
     * for the next match, plus MIN_MATCH bytes to insert the
     * string following the next match.
     */
    if (s.lookahead < MIN_LOOKAHEAD) {
      fill_window(s);
      if (s.lookahead < MIN_LOOKAHEAD && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }
      if (s.lookahead === 0) {
        break; /* flush the current block */
      }
    }

    /* Insert the string window[strstart .. strstart+2] in the
     * dictionary, and set hash_head to the head of the hash chain:
     */
    hash_head = 0/*NIL*/;
    if (s.lookahead >= MIN_MATCH) {
      /*** INSERT_STRING(s, s.strstart, hash_head); ***/
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
      hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
      s.head[s.ins_h] = s.strstart;
      /***/
    }

    /* Find the longest match, discarding those <= prev_length.
     * At this point we have always match_length < MIN_MATCH
     */
    if (hash_head !== 0/*NIL*/ && ((s.strstart - hash_head) <= (s.w_size - MIN_LOOKAHEAD))) {
      /* To simplify the code, we prevent matches with the string
       * of window index 0 (in particular we have to avoid a match
       * of the string with itself at the start of the input file).
       */
      s.match_length = longest_match(s, hash_head);
      /* longest_match() sets match_start */
    }
    if (s.match_length >= MIN_MATCH) {
      // check_match(s, s.strstart, s.match_start, s.match_length); // for debug only

      /*** _tr_tally_dist(s, s.strstart - s.match_start,
                     s.match_length - MIN_MATCH, bflush); ***/
      bflush = trees._tr_tally(s, s.strstart - s.match_start, s.match_length - MIN_MATCH);

      s.lookahead -= s.match_length;

      /* Insert new strings in the hash table only if the match length
       * is not too large. This saves time but degrades compression.
       */
      if (s.match_length <= s.max_lazy_match/*max_insert_length*/ && s.lookahead >= MIN_MATCH) {
        s.match_length--; /* string at strstart already in table */
        do {
          s.strstart++;
          /*** INSERT_STRING(s, s.strstart, hash_head); ***/
          s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
          hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
          s.head[s.ins_h] = s.strstart;
          /***/
          /* strstart never exceeds WSIZE-MAX_MATCH, so there are
           * always MIN_MATCH bytes ahead.
           */
        } while (--s.match_length !== 0);
        s.strstart++;
      } else
      {
        s.strstart += s.match_length;
        s.match_length = 0;
        s.ins_h = s.window[s.strstart];
        /* UPDATE_HASH(s, s.ins_h, s.window[s.strstart+1]); */
        s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + 1]) & s.hash_mask;

//#if MIN_MATCH != 3
//                Call UPDATE_HASH() MIN_MATCH-3 more times
//#endif
        /* If lookahead < MIN_MATCH, ins_h is garbage, but it does not
         * matter since it will be recomputed at next deflate call.
         */
      }
    } else {
      /* No match, output a literal byte */
      //Tracevv((stderr,"%c", s.window[s.strstart]));
      /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
      bflush = trees._tr_tally(s, 0, s.window[s.strstart]);

      s.lookahead--;
      s.strstart++;
    }
    if (bflush) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }
  s.insert = ((s.strstart < (MIN_MATCH - 1)) ? s.strstart : MIN_MATCH - 1);
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }
  return BS_BLOCK_DONE;
}

/* ===========================================================================
 * Same as above, but achieves better compression. We use a lazy
 * evaluation for matches: a match is finally adopted only if there is
 * no better match at the next window position.
 */
function deflate_slow(s, flush) {
  var hash_head;          /* head of hash chain */
  var bflush;              /* set if current block must be flushed */

  var max_insert;

  /* Process the input block. */
  for (;;) {
    /* Make sure that we always have enough lookahead, except
     * at the end of the input file. We need MAX_MATCH bytes
     * for the next match, plus MIN_MATCH bytes to insert the
     * string following the next match.
     */
    if (s.lookahead < MIN_LOOKAHEAD) {
      fill_window(s);
      if (s.lookahead < MIN_LOOKAHEAD && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }
      if (s.lookahead === 0) { break; } /* flush the current block */
    }

    /* Insert the string window[strstart .. strstart+2] in the
     * dictionary, and set hash_head to the head of the hash chain:
     */
    hash_head = 0/*NIL*/;
    if (s.lookahead >= MIN_MATCH) {
      /*** INSERT_STRING(s, s.strstart, hash_head); ***/
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
      hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
      s.head[s.ins_h] = s.strstart;
      /***/
    }

    /* Find the longest match, discarding those <= prev_length.
     */
    s.prev_length = s.match_length;
    s.prev_match = s.match_start;
    s.match_length = MIN_MATCH - 1;

    if (hash_head !== 0/*NIL*/ && s.prev_length < s.max_lazy_match &&
        s.strstart - hash_head <= (s.w_size - MIN_LOOKAHEAD)/*MAX_DIST(s)*/) {
      /* To simplify the code, we prevent matches with the string
       * of window index 0 (in particular we have to avoid a match
       * of the string with itself at the start of the input file).
       */
      s.match_length = longest_match(s, hash_head);
      /* longest_match() sets match_start */

      if (s.match_length <= 5 &&
         (s.strategy === Z_FILTERED || (s.match_length === MIN_MATCH && s.strstart - s.match_start > 4096/*TOO_FAR*/))) {

        /* If prev_match is also MIN_MATCH, match_start is garbage
         * but we will ignore the current match anyway.
         */
        s.match_length = MIN_MATCH - 1;
      }
    }
    /* If there was a match at the previous step and the current
     * match is not better, output the previous match:
     */
    if (s.prev_length >= MIN_MATCH && s.match_length <= s.prev_length) {
      max_insert = s.strstart + s.lookahead - MIN_MATCH;
      /* Do not insert strings in hash table beyond this. */

      //check_match(s, s.strstart-1, s.prev_match, s.prev_length);

      /***_tr_tally_dist(s, s.strstart - 1 - s.prev_match,
                     s.prev_length - MIN_MATCH, bflush);***/
      bflush = trees._tr_tally(s, s.strstart - 1 - s.prev_match, s.prev_length - MIN_MATCH);
      /* Insert in hash table all strings up to the end of the match.
       * strstart-1 and strstart are already inserted. If there is not
       * enough lookahead, the last two strings are not inserted in
       * the hash table.
       */
      s.lookahead -= s.prev_length - 1;
      s.prev_length -= 2;
      do {
        if (++s.strstart <= max_insert) {
          /*** INSERT_STRING(s, s.strstart, hash_head); ***/
          s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
          hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
          s.head[s.ins_h] = s.strstart;
          /***/
        }
      } while (--s.prev_length !== 0);
      s.match_available = 0;
      s.match_length = MIN_MATCH - 1;
      s.strstart++;

      if (bflush) {
        /*** FLUSH_BLOCK(s, 0); ***/
        flush_block_only(s, false);
        if (s.strm.avail_out === 0) {
          return BS_NEED_MORE;
        }
        /***/
      }

    } else if (s.match_available) {
      /* If there was no match at the previous position, output a
       * single literal. If there was a match but the current match
       * is longer, truncate the previous match to a single literal.
       */
      //Tracevv((stderr,"%c", s->window[s->strstart-1]));
      /*** _tr_tally_lit(s, s.window[s.strstart-1], bflush); ***/
      bflush = trees._tr_tally(s, 0, s.window[s.strstart - 1]);

      if (bflush) {
        /*** FLUSH_BLOCK_ONLY(s, 0) ***/
        flush_block_only(s, false);
        /***/
      }
      s.strstart++;
      s.lookahead--;
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
    } else {
      /* There is no previous match to compare with, wait for
       * the next step to decide.
       */
      s.match_available = 1;
      s.strstart++;
      s.lookahead--;
    }
  }
  //Assert (flush != Z_NO_FLUSH, "no flush?");
  if (s.match_available) {
    //Tracevv((stderr,"%c", s->window[s->strstart-1]));
    /*** _tr_tally_lit(s, s.window[s.strstart-1], bflush); ***/
    bflush = trees._tr_tally(s, 0, s.window[s.strstart - 1]);

    s.match_available = 0;
  }
  s.insert = s.strstart < MIN_MATCH - 1 ? s.strstart : MIN_MATCH - 1;
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }

  return BS_BLOCK_DONE;
}


/* ===========================================================================
 * For Z_RLE, simply look for runs of bytes, generate matches only of distance
 * one.  Do not maintain a hash table.  (It will be regenerated if this run of
 * deflate switches away from Z_RLE.)
 */
function deflate_rle(s, flush) {
  var bflush;            /* set if current block must be flushed */
  var prev;              /* byte at distance one to match */
  var scan, strend;      /* scan goes up to strend for length of run */

  var _win = s.window;

  for (;;) {
    /* Make sure that we always have enough lookahead, except
     * at the end of the input file. We need MAX_MATCH bytes
     * for the longest run, plus one for the unrolled loop.
     */
    if (s.lookahead <= MAX_MATCH) {
      fill_window(s);
      if (s.lookahead <= MAX_MATCH && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }
      if (s.lookahead === 0) { break; } /* flush the current block */
    }

    /* See how many times the previous byte repeats */
    s.match_length = 0;
    if (s.lookahead >= MIN_MATCH && s.strstart > 0) {
      scan = s.strstart - 1;
      prev = _win[scan];
      if (prev === _win[++scan] && prev === _win[++scan] && prev === _win[++scan]) {
        strend = s.strstart + MAX_MATCH;
        do {
          /*jshint noempty:false*/
        } while (prev === _win[++scan] && prev === _win[++scan] &&
                 prev === _win[++scan] && prev === _win[++scan] &&
                 prev === _win[++scan] && prev === _win[++scan] &&
                 prev === _win[++scan] && prev === _win[++scan] &&
                 scan < strend);
        s.match_length = MAX_MATCH - (strend - scan);
        if (s.match_length > s.lookahead) {
          s.match_length = s.lookahead;
        }
      }
      //Assert(scan <= s->window+(uInt)(s->window_size-1), "wild scan");
    }

    /* Emit match if have run of MIN_MATCH or longer, else emit literal */
    if (s.match_length >= MIN_MATCH) {
      //check_match(s, s.strstart, s.strstart - 1, s.match_length);

      /*** _tr_tally_dist(s, 1, s.match_length - MIN_MATCH, bflush); ***/
      bflush = trees._tr_tally(s, 1, s.match_length - MIN_MATCH);

      s.lookahead -= s.match_length;
      s.strstart += s.match_length;
      s.match_length = 0;
    } else {
      /* No match, output a literal byte */
      //Tracevv((stderr,"%c", s->window[s->strstart]));
      /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
      bflush = trees._tr_tally(s, 0, s.window[s.strstart]);

      s.lookahead--;
      s.strstart++;
    }
    if (bflush) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }
  s.insert = 0;
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }
  return BS_BLOCK_DONE;
}

/* ===========================================================================
 * For Z_HUFFMAN_ONLY, do not look for matches.  Do not maintain a hash table.
 * (It will be regenerated if this run of deflate switches away from Huffman.)
 */
function deflate_huff(s, flush) {
  var bflush;             /* set if current block must be flushed */

  for (;;) {
    /* Make sure that we have a literal to write. */
    if (s.lookahead === 0) {
      fill_window(s);
      if (s.lookahead === 0) {
        if (flush === Z_NO_FLUSH) {
          return BS_NEED_MORE;
        }
        break;      /* flush the current block */
      }
    }

    /* Output a literal byte */
    s.match_length = 0;
    //Tracevv((stderr,"%c", s->window[s->strstart]));
    /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
    bflush = trees._tr_tally(s, 0, s.window[s.strstart]);
    s.lookahead--;
    s.strstart++;
    if (bflush) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }
  s.insert = 0;
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }
  return BS_BLOCK_DONE;
}

/* Values for max_lazy_match, good_match and max_chain_length, depending on
 * the desired pack level (0..9). The values given below have been tuned to
 * exclude worst case performance for pathological files. Better values may be
 * found for specific files.
 */
function Config(good_length, max_lazy, nice_length, max_chain, func) {
  this.good_length = good_length;
  this.max_lazy = max_lazy;
  this.nice_length = nice_length;
  this.max_chain = max_chain;
  this.func = func;
}

var configuration_table;

configuration_table = [
  /*      good lazy nice chain */
  new Config(0, 0, 0, 0, deflate_stored),          /* 0 store only */
  new Config(4, 4, 8, 4, deflate_fast),            /* 1 max speed, no lazy matches */
  new Config(4, 5, 16, 8, deflate_fast),           /* 2 */
  new Config(4, 6, 32, 32, deflate_fast),          /* 3 */

  new Config(4, 4, 16, 16, deflate_slow),          /* 4 lazy matches */
  new Config(8, 16, 32, 32, deflate_slow),         /* 5 */
  new Config(8, 16, 128, 128, deflate_slow),       /* 6 */
  new Config(8, 32, 128, 256, deflate_slow),       /* 7 */
  new Config(32, 128, 258, 1024, deflate_slow),    /* 8 */
  new Config(32, 258, 258, 4096, deflate_slow)     /* 9 max compression */
];


/* ===========================================================================
 * Initialize the "longest match" routines for a new zlib stream
 */
function lm_init(s) {
  s.window_size = 2 * s.w_size;

  /*** CLEAR_HASH(s); ***/
  zero(s.head); // Fill with NIL (= 0);

  /* Set the default configuration parameters:
   */
  s.max_lazy_match = configuration_table[s.level].max_lazy;
  s.good_match = configuration_table[s.level].good_length;
  s.nice_match = configuration_table[s.level].nice_length;
  s.max_chain_length = configuration_table[s.level].max_chain;

  s.strstart = 0;
  s.block_start = 0;
  s.lookahead = 0;
  s.insert = 0;
  s.match_length = s.prev_length = MIN_MATCH - 1;
  s.match_available = 0;
  s.ins_h = 0;
}


function DeflateState() {
  this.strm = null;            /* pointer back to this zlib stream */
  this.status = 0;            /* as the name implies */
  this.pending_buf = null;      /* output still pending */
  this.pending_buf_size = 0;  /* size of pending_buf */
  this.pending_out = 0;       /* next pending byte to output to the stream */
  this.pending = 0;           /* nb of bytes in the pending buffer */
  this.wrap = 0;              /* bit 0 true for zlib, bit 1 true for gzip */
  this.gzhead = null;         /* gzip header information to write */
  this.gzindex = 0;           /* where in extra, name, or comment */
  this.method = Z_DEFLATED; /* can only be DEFLATED */
  this.last_flush = -1;   /* value of flush param for previous deflate call */

  this.w_size = 0;  /* LZ77 window size (32K by default) */
  this.w_bits = 0;  /* log2(w_size)  (8..16) */
  this.w_mask = 0;  /* w_size - 1 */

  this.window = null;
  /* Sliding window. Input bytes are read into the second half of the window,
   * and move to the first half later to keep a dictionary of at least wSize
   * bytes. With this organization, matches are limited to a distance of
   * wSize-MAX_MATCH bytes, but this ensures that IO is always
   * performed with a length multiple of the block size.
   */

  this.window_size = 0;
  /* Actual size of window: 2*wSize, except when the user input buffer
   * is directly used as sliding window.
   */

  this.prev = null;
  /* Link to older string with same hash index. To limit the size of this
   * array to 64K, this link is maintained only for the last 32K strings.
   * An index in this array is thus a window index modulo 32K.
   */

  this.head = null;   /* Heads of the hash chains or NIL. */

  this.ins_h = 0;       /* hash index of string to be inserted */
  this.hash_size = 0;   /* number of elements in hash table */
  this.hash_bits = 0;   /* log2(hash_size) */
  this.hash_mask = 0;   /* hash_size-1 */

  this.hash_shift = 0;
  /* Number of bits by which ins_h must be shifted at each input
   * step. It must be such that after MIN_MATCH steps, the oldest
   * byte no longer takes part in the hash key, that is:
   *   hash_shift * MIN_MATCH >= hash_bits
   */

  this.block_start = 0;
  /* Window position at the beginning of the current output block. Gets
   * negative when the window is moved backwards.
   */

  this.match_length = 0;      /* length of best match */
  this.prev_match = 0;        /* previous match */
  this.match_available = 0;   /* set if previous match exists */
  this.strstart = 0;          /* start of string to insert */
  this.match_start = 0;       /* start of matching string */
  this.lookahead = 0;         /* number of valid bytes ahead in window */

  this.prev_length = 0;
  /* Length of the best match at previous step. Matches not greater than this
   * are discarded. This is used in the lazy match evaluation.
   */

  this.max_chain_length = 0;
  /* To speed up deflation, hash chains are never searched beyond this
   * length.  A higher limit improves compression ratio but degrades the
   * speed.
   */

  this.max_lazy_match = 0;
  /* Attempt to find a better match only when the current match is strictly
   * smaller than this value. This mechanism is used only for compression
   * levels >= 4.
   */
  // That's alias to max_lazy_match, don't use directly
  //this.max_insert_length = 0;
  /* Insert new strings in the hash table only if the match length is not
   * greater than this length. This saves time but degrades compression.
   * max_insert_length is used only for compression levels <= 3.
   */

  this.level = 0;     /* compression level (1..9) */
  this.strategy = 0;  /* favor or force Huffman coding*/

  this.good_match = 0;
  /* Use a faster search when the previous match is longer than this */

  this.nice_match = 0; /* Stop searching when current match exceeds this */

              /* used by trees.c: */

  /* Didn't use ct_data typedef below to suppress compiler warning */

  // struct ct_data_s dyn_ltree[HEAP_SIZE];   /* literal and length tree */
  // struct ct_data_s dyn_dtree[2*D_CODES+1]; /* distance tree */
  // struct ct_data_s bl_tree[2*BL_CODES+1];  /* Huffman tree for bit lengths */

  // Use flat array of DOUBLE size, with interleaved fata,
  // because JS does not support effective
  this.dyn_ltree  = new utils.Buf16(HEAP_SIZE * 2);
  this.dyn_dtree  = new utils.Buf16((2 * D_CODES + 1) * 2);
  this.bl_tree    = new utils.Buf16((2 * BL_CODES + 1) * 2);
  zero(this.dyn_ltree);
  zero(this.dyn_dtree);
  zero(this.bl_tree);

  this.l_desc   = null;         /* desc. for literal tree */
  this.d_desc   = null;         /* desc. for distance tree */
  this.bl_desc  = null;         /* desc. for bit length tree */

  //ush bl_count[MAX_BITS+1];
  this.bl_count = new utils.Buf16(MAX_BITS + 1);
  /* number of codes at each bit length for an optimal tree */

  //int heap[2*L_CODES+1];      /* heap used to build the Huffman trees */
  this.heap = new utils.Buf16(2 * L_CODES + 1);  /* heap used to build the Huffman trees */
  zero(this.heap);

  this.heap_len = 0;               /* number of elements in the heap */
  this.heap_max = 0;               /* element of largest frequency */
  /* The sons of heap[n] are heap[2*n] and heap[2*n+1]. heap[0] is not used.
   * The same heap array is used to build all trees.
   */

  this.depth = new utils.Buf16(2 * L_CODES + 1); //uch depth[2*L_CODES+1];
  zero(this.depth);
  /* Depth of each subtree used as tie breaker for trees of equal frequency
   */

  this.l_buf = 0;          /* buffer index for literals or lengths */

  this.lit_bufsize = 0;
  /* Size of match buffer for literals/lengths.  There are 4 reasons for
   * limiting lit_bufsize to 64K:
   *   - frequencies can be kept in 16 bit counters
   *   - if compression is not successful for the first block, all input
   *     data is still in the window so we can still emit a stored block even
   *     when input comes from standard input.  (This can also be done for
   *     all blocks if lit_bufsize is not greater than 32K.)
   *   - if compression is not successful for a file smaller than 64K, we can
   *     even emit a stored file instead of a stored block (saving 5 bytes).
   *     This is applicable only for zip (not gzip or zlib).
   *   - creating new Huffman trees less frequently may not provide fast
   *     adaptation to changes in the input data statistics. (Take for
   *     example a binary file with poorly compressible code followed by
   *     a highly compressible string table.) Smaller buffer sizes give
   *     fast adaptation but have of course the overhead of transmitting
   *     trees more frequently.
   *   - I can't count above 4
   */

  this.last_lit = 0;      /* running index in l_buf */

  this.d_buf = 0;
  /* Buffer index for distances. To simplify the code, d_buf and l_buf have
   * the same number of elements. To use different lengths, an extra flag
   * array would be necessary.
   */

  this.opt_len = 0;       /* bit length of current block with optimal trees */
  this.static_len = 0;    /* bit length of current block with static trees */
  this.matches = 0;       /* number of string matches in current block */
  this.insert = 0;        /* bytes at end of window left to insert */


  this.bi_buf = 0;
  /* Output buffer. bits are inserted starting at the bottom (least
   * significant bits).
   */
  this.bi_valid = 0;
  /* Number of valid bits in bi_buf.  All bits above the last valid bit
   * are always zero.
   */

  // Used for window memory init. We safely ignore it for JS. That makes
  // sense only for pointers and memory check tools.
  //this.high_water = 0;
  /* High water mark offset in window for initialized bytes -- bytes above
   * this are set to zero in order to avoid memory check warnings when
   * longest match routines access bytes past the input.  This is then
   * updated to the new high water mark.
   */
}


function deflateResetKeep(strm) {
  var s;

  if (!strm || !strm.state) {
    return err(strm, Z_STREAM_ERROR);
  }

  strm.total_in = strm.total_out = 0;
  strm.data_type = Z_UNKNOWN;

  s = strm.state;
  s.pending = 0;
  s.pending_out = 0;

  if (s.wrap < 0) {
    s.wrap = -s.wrap;
    /* was made negative by deflate(..., Z_FINISH); */
  }
  s.status = (s.wrap ? INIT_STATE : BUSY_STATE);
  strm.adler = (s.wrap === 2) ?
    0  // crc32(0, Z_NULL, 0)
  :
    1; // adler32(0, Z_NULL, 0)
  s.last_flush = Z_NO_FLUSH;
  trees._tr_init(s);
  return Z_OK;
}


function deflateReset(strm) {
  var ret = deflateResetKeep(strm);
  if (ret === Z_OK) {
    lm_init(strm.state);
  }
  return ret;
}


function deflateSetHeader(strm, head) {
  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  if (strm.state.wrap !== 2) { return Z_STREAM_ERROR; }
  strm.state.gzhead = head;
  return Z_OK;
}


function deflateInit2(strm, level, method, windowBits, memLevel, strategy) {
  if (!strm) { // === Z_NULL
    return Z_STREAM_ERROR;
  }
  var wrap = 1;

  if (level === Z_DEFAULT_COMPRESSION) {
    level = 6;
  }

  if (windowBits < 0) { /* suppress zlib wrapper */
    wrap = 0;
    windowBits = -windowBits;
  }

  else if (windowBits > 15) {
    wrap = 2;           /* write gzip wrapper instead */
    windowBits -= 16;
  }


  if (memLevel < 1 || memLevel > MAX_MEM_LEVEL || method !== Z_DEFLATED ||
    windowBits < 8 || windowBits > 15 || level < 0 || level > 9 ||
    strategy < 0 || strategy > Z_FIXED) {
    return err(strm, Z_STREAM_ERROR);
  }


  if (windowBits === 8) {
    windowBits = 9;
  }
  /* until 256-byte window bug fixed */

  var s = new DeflateState();

  strm.state = s;
  s.strm = strm;

  s.wrap = wrap;
  s.gzhead = null;
  s.w_bits = windowBits;
  s.w_size = 1 << s.w_bits;
  s.w_mask = s.w_size - 1;

  s.hash_bits = memLevel + 7;
  s.hash_size = 1 << s.hash_bits;
  s.hash_mask = s.hash_size - 1;
  s.hash_shift = ~~((s.hash_bits + MIN_MATCH - 1) / MIN_MATCH);

  s.window = new utils.Buf8(s.w_size * 2);
  s.head = new utils.Buf16(s.hash_size);
  s.prev = new utils.Buf16(s.w_size);

  // Don't need mem init magic for JS.
  //s.high_water = 0;  /* nothing written to s->window yet */

  s.lit_bufsize = 1 << (memLevel + 6); /* 16K elements by default */

  s.pending_buf_size = s.lit_bufsize * 4;

  //overlay = (ushf *) ZALLOC(strm, s->lit_bufsize, sizeof(ush)+2);
  //s->pending_buf = (uchf *) overlay;
  s.pending_buf = new utils.Buf8(s.pending_buf_size);

  // It is offset from `s.pending_buf` (size is `s.lit_bufsize * 2`)
  //s->d_buf = overlay + s->lit_bufsize/sizeof(ush);
  s.d_buf = 1 * s.lit_bufsize;

  //s->l_buf = s->pending_buf + (1+sizeof(ush))*s->lit_bufsize;
  s.l_buf = (1 + 2) * s.lit_bufsize;

  s.level = level;
  s.strategy = strategy;
  s.method = method;

  return deflateReset(strm);
}

function deflateInit(strm, level) {
  return deflateInit2(strm, level, Z_DEFLATED, MAX_WBITS, DEF_MEM_LEVEL, Z_DEFAULT_STRATEGY);
}


function deflate(strm, flush) {
  var old_flush, s;
  var beg, val; // for gzip header write only

  if (!strm || !strm.state ||
    flush > Z_BLOCK || flush < 0) {
    return strm ? err(strm, Z_STREAM_ERROR) : Z_STREAM_ERROR;
  }

  s = strm.state;

  if (!strm.output ||
      (!strm.input && strm.avail_in !== 0) ||
      (s.status === FINISH_STATE && flush !== Z_FINISH)) {
    return err(strm, (strm.avail_out === 0) ? Z_BUF_ERROR : Z_STREAM_ERROR);
  }

  s.strm = strm; /* just in case */
  old_flush = s.last_flush;
  s.last_flush = flush;

  /* Write the header */
  if (s.status === INIT_STATE) {

    if (s.wrap === 2) { // GZIP header
      strm.adler = 0;  //crc32(0L, Z_NULL, 0);
      put_byte(s, 31);
      put_byte(s, 139);
      put_byte(s, 8);
      if (!s.gzhead) { // s->gzhead == Z_NULL
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, s.level === 9 ? 2 :
                    (s.strategy >= Z_HUFFMAN_ONLY || s.level < 2 ?
                     4 : 0));
        put_byte(s, OS_CODE);
        s.status = BUSY_STATE;
      }
      else {
        put_byte(s, (s.gzhead.text ? 1 : 0) +
                    (s.gzhead.hcrc ? 2 : 0) +
                    (!s.gzhead.extra ? 0 : 4) +
                    (!s.gzhead.name ? 0 : 8) +
                    (!s.gzhead.comment ? 0 : 16)
        );
        put_byte(s, s.gzhead.time & 0xff);
        put_byte(s, (s.gzhead.time >> 8) & 0xff);
        put_byte(s, (s.gzhead.time >> 16) & 0xff);
        put_byte(s, (s.gzhead.time >> 24) & 0xff);
        put_byte(s, s.level === 9 ? 2 :
                    (s.strategy >= Z_HUFFMAN_ONLY || s.level < 2 ?
                     4 : 0));
        put_byte(s, s.gzhead.os & 0xff);
        if (s.gzhead.extra && s.gzhead.extra.length) {
          put_byte(s, s.gzhead.extra.length & 0xff);
          put_byte(s, (s.gzhead.extra.length >> 8) & 0xff);
        }
        if (s.gzhead.hcrc) {
          strm.adler = crc32(strm.adler, s.pending_buf, s.pending, 0);
        }
        s.gzindex = 0;
        s.status = EXTRA_STATE;
      }
    }
    else // DEFLATE header
    {
      var header = (Z_DEFLATED + ((s.w_bits - 8) << 4)) << 8;
      var level_flags = -1;

      if (s.strategy >= Z_HUFFMAN_ONLY || s.level < 2) {
        level_flags = 0;
      } else if (s.level < 6) {
        level_flags = 1;
      } else if (s.level === 6) {
        level_flags = 2;
      } else {
        level_flags = 3;
      }
      header |= (level_flags << 6);
      if (s.strstart !== 0) { header |= PRESET_DICT; }
      header += 31 - (header % 31);

      s.status = BUSY_STATE;
      putShortMSB(s, header);

      /* Save the adler32 of the preset dictionary: */
      if (s.strstart !== 0) {
        putShortMSB(s, strm.adler >>> 16);
        putShortMSB(s, strm.adler & 0xffff);
      }
      strm.adler = 1; // adler32(0L, Z_NULL, 0);
    }
  }

//#ifdef GZIP
  if (s.status === EXTRA_STATE) {
    if (s.gzhead.extra/* != Z_NULL*/) {
      beg = s.pending;  /* start of bytes to update crc */

      while (s.gzindex < (s.gzhead.extra.length & 0xffff)) {
        if (s.pending === s.pending_buf_size) {
          if (s.gzhead.hcrc && s.pending > beg) {
            strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
          }
          flush_pending(strm);
          beg = s.pending;
          if (s.pending === s.pending_buf_size) {
            break;
          }
        }
        put_byte(s, s.gzhead.extra[s.gzindex] & 0xff);
        s.gzindex++;
      }
      if (s.gzhead.hcrc && s.pending > beg) {
        strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
      }
      if (s.gzindex === s.gzhead.extra.length) {
        s.gzindex = 0;
        s.status = NAME_STATE;
      }
    }
    else {
      s.status = NAME_STATE;
    }
  }
  if (s.status === NAME_STATE) {
    if (s.gzhead.name/* != Z_NULL*/) {
      beg = s.pending;  /* start of bytes to update crc */
      //int val;

      do {
        if (s.pending === s.pending_buf_size) {
          if (s.gzhead.hcrc && s.pending > beg) {
            strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
          }
          flush_pending(strm);
          beg = s.pending;
          if (s.pending === s.pending_buf_size) {
            val = 1;
            break;
          }
        }
        // JS specific: little magic to add zero terminator to end of string
        if (s.gzindex < s.gzhead.name.length) {
          val = s.gzhead.name.charCodeAt(s.gzindex++) & 0xff;
        } else {
          val = 0;
        }
        put_byte(s, val);
      } while (val !== 0);

      if (s.gzhead.hcrc && s.pending > beg) {
        strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
      }
      if (val === 0) {
        s.gzindex = 0;
        s.status = COMMENT_STATE;
      }
    }
    else {
      s.status = COMMENT_STATE;
    }
  }
  if (s.status === COMMENT_STATE) {
    if (s.gzhead.comment/* != Z_NULL*/) {
      beg = s.pending;  /* start of bytes to update crc */
      //int val;

      do {
        if (s.pending === s.pending_buf_size) {
          if (s.gzhead.hcrc && s.pending > beg) {
            strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
          }
          flush_pending(strm);
          beg = s.pending;
          if (s.pending === s.pending_buf_size) {
            val = 1;
            break;
          }
        }
        // JS specific: little magic to add zero terminator to end of string
        if (s.gzindex < s.gzhead.comment.length) {
          val = s.gzhead.comment.charCodeAt(s.gzindex++) & 0xff;
        } else {
          val = 0;
        }
        put_byte(s, val);
      } while (val !== 0);

      if (s.gzhead.hcrc && s.pending > beg) {
        strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
      }
      if (val === 0) {
        s.status = HCRC_STATE;
      }
    }
    else {
      s.status = HCRC_STATE;
    }
  }
  if (s.status === HCRC_STATE) {
    if (s.gzhead.hcrc) {
      if (s.pending + 2 > s.pending_buf_size) {
        flush_pending(strm);
      }
      if (s.pending + 2 <= s.pending_buf_size) {
        put_byte(s, strm.adler & 0xff);
        put_byte(s, (strm.adler >> 8) & 0xff);
        strm.adler = 0; //crc32(0L, Z_NULL, 0);
        s.status = BUSY_STATE;
      }
    }
    else {
      s.status = BUSY_STATE;
    }
  }
//#endif

  /* Flush as much pending output as possible */
  if (s.pending !== 0) {
    flush_pending(strm);
    if (strm.avail_out === 0) {
      /* Since avail_out is 0, deflate will be called again with
       * more output space, but possibly with both pending and
       * avail_in equal to zero. There won't be anything to do,
       * but this is not an error situation so make sure we
       * return OK instead of BUF_ERROR at next call of deflate:
       */
      s.last_flush = -1;
      return Z_OK;
    }

    /* Make sure there is something to do and avoid duplicate consecutive
     * flushes. For repeated and useless calls with Z_FINISH, we keep
     * returning Z_STREAM_END instead of Z_BUF_ERROR.
     */
  } else if (strm.avail_in === 0 && rank(flush) <= rank(old_flush) &&
    flush !== Z_FINISH) {
    return err(strm, Z_BUF_ERROR);
  }

  /* User must not provide more input after the first FINISH: */
  if (s.status === FINISH_STATE && strm.avail_in !== 0) {
    return err(strm, Z_BUF_ERROR);
  }

  /* Start a new block or continue the current one.
   */
  if (strm.avail_in !== 0 || s.lookahead !== 0 ||
    (flush !== Z_NO_FLUSH && s.status !== FINISH_STATE)) {
    var bstate = (s.strategy === Z_HUFFMAN_ONLY) ? deflate_huff(s, flush) :
      (s.strategy === Z_RLE ? deflate_rle(s, flush) :
        configuration_table[s.level].func(s, flush));

    if (bstate === BS_FINISH_STARTED || bstate === BS_FINISH_DONE) {
      s.status = FINISH_STATE;
    }
    if (bstate === BS_NEED_MORE || bstate === BS_FINISH_STARTED) {
      if (strm.avail_out === 0) {
        s.last_flush = -1;
        /* avoid BUF_ERROR next call, see above */
      }
      return Z_OK;
      /* If flush != Z_NO_FLUSH && avail_out == 0, the next call
       * of deflate should use the same flush parameter to make sure
       * that the flush is complete. So we don't have to output an
       * empty block here, this will be done at next call. This also
       * ensures that for a very small output buffer, we emit at most
       * one empty block.
       */
    }
    if (bstate === BS_BLOCK_DONE) {
      if (flush === Z_PARTIAL_FLUSH) {
        trees._tr_align(s);
      }
      else if (flush !== Z_BLOCK) { /* FULL_FLUSH or SYNC_FLUSH */

        trees._tr_stored_block(s, 0, 0, false);
        /* For a full flush, this empty block will be recognized
         * as a special marker by inflate_sync().
         */
        if (flush === Z_FULL_FLUSH) {
          /*** CLEAR_HASH(s); ***/             /* forget history */
          zero(s.head); // Fill with NIL (= 0);

          if (s.lookahead === 0) {
            s.strstart = 0;
            s.block_start = 0;
            s.insert = 0;
          }
        }
      }
      flush_pending(strm);
      if (strm.avail_out === 0) {
        s.last_flush = -1; /* avoid BUF_ERROR at next call, see above */
        return Z_OK;
      }
    }
  }
  //Assert(strm->avail_out > 0, "bug2");
  //if (strm.avail_out <= 0) { throw new Error("bug2");}

  if (flush !== Z_FINISH) { return Z_OK; }
  if (s.wrap <= 0) { return Z_STREAM_END; }

  /* Write the trailer */
  if (s.wrap === 2) {
    put_byte(s, strm.adler & 0xff);
    put_byte(s, (strm.adler >> 8) & 0xff);
    put_byte(s, (strm.adler >> 16) & 0xff);
    put_byte(s, (strm.adler >> 24) & 0xff);
    put_byte(s, strm.total_in & 0xff);
    put_byte(s, (strm.total_in >> 8) & 0xff);
    put_byte(s, (strm.total_in >> 16) & 0xff);
    put_byte(s, (strm.total_in >> 24) & 0xff);
  }
  else
  {
    putShortMSB(s, strm.adler >>> 16);
    putShortMSB(s, strm.adler & 0xffff);
  }

  flush_pending(strm);
  /* If avail_out is zero, the application will call deflate again
   * to flush the rest.
   */
  if (s.wrap > 0) { s.wrap = -s.wrap; }
  /* write the trailer only once! */
  return s.pending !== 0 ? Z_OK : Z_STREAM_END;
}

function deflateEnd(strm) {
  var status;

  if (!strm/*== Z_NULL*/ || !strm.state/*== Z_NULL*/) {
    return Z_STREAM_ERROR;
  }

  status = strm.state.status;
  if (status !== INIT_STATE &&
    status !== EXTRA_STATE &&
    status !== NAME_STATE &&
    status !== COMMENT_STATE &&
    status !== HCRC_STATE &&
    status !== BUSY_STATE &&
    status !== FINISH_STATE
  ) {
    return err(strm, Z_STREAM_ERROR);
  }

  strm.state = null;

  return status === BUSY_STATE ? err(strm, Z_DATA_ERROR) : Z_OK;
}


/* =========================================================================
 * Initializes the compression dictionary from the given byte
 * sequence without producing any compressed output.
 */
function deflateSetDictionary(strm, dictionary) {
  var dictLength = dictionary.length;

  var s;
  var str, n;
  var wrap;
  var avail;
  var next;
  var input;
  var tmpDict;

  if (!strm/*== Z_NULL*/ || !strm.state/*== Z_NULL*/) {
    return Z_STREAM_ERROR;
  }

  s = strm.state;
  wrap = s.wrap;

  if (wrap === 2 || (wrap === 1 && s.status !== INIT_STATE) || s.lookahead) {
    return Z_STREAM_ERROR;
  }

  /* when using zlib wrappers, compute Adler-32 for provided dictionary */
  if (wrap === 1) {
    /* adler32(strm->adler, dictionary, dictLength); */
    strm.adler = adler32(strm.adler, dictionary, dictLength, 0);
  }

  s.wrap = 0;   /* avoid computing Adler-32 in read_buf */

  /* if dictionary would fill window, just replace the history */
  if (dictLength >= s.w_size) {
    if (wrap === 0) {            /* already empty otherwise */
      /*** CLEAR_HASH(s); ***/
      zero(s.head); // Fill with NIL (= 0);
      s.strstart = 0;
      s.block_start = 0;
      s.insert = 0;
    }
    /* use the tail */
    // dictionary = dictionary.slice(dictLength - s.w_size);
    tmpDict = new utils.Buf8(s.w_size);
    utils.arraySet(tmpDict, dictionary, dictLength - s.w_size, s.w_size, 0);
    dictionary = tmpDict;
    dictLength = s.w_size;
  }
  /* insert dictionary into window and hash */
  avail = strm.avail_in;
  next = strm.next_in;
  input = strm.input;
  strm.avail_in = dictLength;
  strm.next_in = 0;
  strm.input = dictionary;
  fill_window(s);
  while (s.lookahead >= MIN_MATCH) {
    str = s.strstart;
    n = s.lookahead - (MIN_MATCH - 1);
    do {
      /* UPDATE_HASH(s, s->ins_h, s->window[str + MIN_MATCH-1]); */
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[str + MIN_MATCH - 1]) & s.hash_mask;

      s.prev[str & s.w_mask] = s.head[s.ins_h];

      s.head[s.ins_h] = str;
      str++;
    } while (--n);
    s.strstart = str;
    s.lookahead = MIN_MATCH - 1;
    fill_window(s);
  }
  s.strstart += s.lookahead;
  s.block_start = s.strstart;
  s.insert = s.lookahead;
  s.lookahead = 0;
  s.match_length = s.prev_length = MIN_MATCH - 1;
  s.match_available = 0;
  strm.next_in = next;
  strm.input = input;
  strm.avail_in = avail;
  s.wrap = wrap;
  return Z_OK;
}


exports.deflateInit = deflateInit;
exports.deflateInit2 = deflateInit2;
exports.deflateReset = deflateReset;
exports.deflateResetKeep = deflateResetKeep;
exports.deflateSetHeader = deflateSetHeader;
exports.deflate = deflate;
exports.deflateEnd = deflateEnd;
exports.deflateSetDictionary = deflateSetDictionary;
exports.deflateInfo = 'pako deflate (from Nodeca project)';

/* Not implemented
exports.deflateBound = deflateBound;
exports.deflateCopy = deflateCopy;
exports.deflateParams = deflateParams;
exports.deflatePending = deflatePending;
exports.deflatePrime = deflatePrime;
exports.deflateTune = deflateTune;
*/


/***/ }),
/* 356 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

/* eslint-disable space-unary-ops */

var utils = __webpack_require__(126);

/* Public constants ==========================================================*/
/* ===========================================================================*/


//var Z_FILTERED          = 1;
//var Z_HUFFMAN_ONLY      = 2;
//var Z_RLE               = 3;
var Z_FIXED               = 4;
//var Z_DEFAULT_STRATEGY  = 0;

/* Possible values of the data_type field (though see inflate()) */
var Z_BINARY              = 0;
var Z_TEXT                = 1;
//var Z_ASCII             = 1; // = Z_TEXT
var Z_UNKNOWN             = 2;

/*============================================================================*/


function zero(buf) { var len = buf.length; while (--len >= 0) { buf[len] = 0; } }

// From zutil.h

var STORED_BLOCK = 0;
var STATIC_TREES = 1;
var DYN_TREES    = 2;
/* The three kinds of block type */

var MIN_MATCH    = 3;
var MAX_MATCH    = 258;
/* The minimum and maximum match lengths */

// From deflate.h
/* ===========================================================================
 * Internal compression state.
 */

var LENGTH_CODES  = 29;
/* number of length codes, not counting the special END_BLOCK code */

var LITERALS      = 256;
/* number of literal bytes 0..255 */

var L_CODES       = LITERALS + 1 + LENGTH_CODES;
/* number of Literal or Length codes, including the END_BLOCK code */

var D_CODES       = 30;
/* number of distance codes */

var BL_CODES      = 19;
/* number of codes used to transfer the bit lengths */

var HEAP_SIZE     = 2 * L_CODES + 1;
/* maximum heap size */

var MAX_BITS      = 15;
/* All codes must not exceed MAX_BITS bits */

var Buf_size      = 16;
/* size of bit buffer in bi_buf */


/* ===========================================================================
 * Constants
 */

var MAX_BL_BITS = 7;
/* Bit length codes must not exceed MAX_BL_BITS bits */

var END_BLOCK   = 256;
/* end of block literal code */

var REP_3_6     = 16;
/* repeat previous bit length 3-6 times (2 bits of repeat count) */

var REPZ_3_10   = 17;
/* repeat a zero length 3-10 times  (3 bits of repeat count) */

var REPZ_11_138 = 18;
/* repeat a zero length 11-138 times  (7 bits of repeat count) */

/* eslint-disable comma-spacing,array-bracket-spacing */
var extra_lbits =   /* extra bits for each length code */
  [0,0,0,0,0,0,0,0,1,1,1,1,2,2,2,2,3,3,3,3,4,4,4,4,5,5,5,5,0];

var extra_dbits =   /* extra bits for each distance code */
  [0,0,0,0,1,1,2,2,3,3,4,4,5,5,6,6,7,7,8,8,9,9,10,10,11,11,12,12,13,13];

var extra_blbits =  /* extra bits for each bit length code */
  [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,2,3,7];

var bl_order =
  [16,17,18,0,8,7,9,6,10,5,11,4,12,3,13,2,14,1,15];
/* eslint-enable comma-spacing,array-bracket-spacing */

/* The lengths of the bit length codes are sent in order of decreasing
 * probability, to avoid transmitting the lengths for unused bit length codes.
 */

/* ===========================================================================
 * Local data. These are initialized only once.
 */

// We pre-fill arrays with 0 to avoid uninitialized gaps

var DIST_CODE_LEN = 512; /* see definition of array dist_code below */

// !!!! Use flat array instead of structure, Freq = i*2, Len = i*2+1
var static_ltree  = new Array((L_CODES + 2) * 2);
zero(static_ltree);
/* The static literal tree. Since the bit lengths are imposed, there is no
 * need for the L_CODES extra codes used during heap construction. However
 * The codes 286 and 287 are needed to build a canonical tree (see _tr_init
 * below).
 */

var static_dtree  = new Array(D_CODES * 2);
zero(static_dtree);
/* The static distance tree. (Actually a trivial tree since all codes use
 * 5 bits.)
 */

var _dist_code    = new Array(DIST_CODE_LEN);
zero(_dist_code);
/* Distance codes. The first 256 values correspond to the distances
 * 3 .. 258, the last 256 values correspond to the top 8 bits of
 * the 15 bit distances.
 */

var _length_code  = new Array(MAX_MATCH - MIN_MATCH + 1);
zero(_length_code);
/* length code for each normalized match length (0 == MIN_MATCH) */

var base_length   = new Array(LENGTH_CODES);
zero(base_length);
/* First normalized length for each code (0 = MIN_MATCH) */

var base_dist     = new Array(D_CODES);
zero(base_dist);
/* First normalized distance for each code (0 = distance of 1) */


function StaticTreeDesc(static_tree, extra_bits, extra_base, elems, max_length) {

  this.static_tree  = static_tree;  /* static tree or NULL */
  this.extra_bits   = extra_bits;   /* extra bits for each code or NULL */
  this.extra_base   = extra_base;   /* base index for extra_bits */
  this.elems        = elems;        /* max number of elements in the tree */
  this.max_length   = max_length;   /* max bit length for the codes */

  // show if `static_tree` has data or dummy - needed for monomorphic objects
  this.has_stree    = static_tree && static_tree.length;
}


var static_l_desc;
var static_d_desc;
var static_bl_desc;


function TreeDesc(dyn_tree, stat_desc) {
  this.dyn_tree = dyn_tree;     /* the dynamic tree */
  this.max_code = 0;            /* largest code with non zero frequency */
  this.stat_desc = stat_desc;   /* the corresponding static tree */
}



function d_code(dist) {
  return dist < 256 ? _dist_code[dist] : _dist_code[256 + (dist >>> 7)];
}


/* ===========================================================================
 * Output a short LSB first on the stream.
 * IN assertion: there is enough room in pendingBuf.
 */
function put_short(s, w) {
//    put_byte(s, (uch)((w) & 0xff));
//    put_byte(s, (uch)((ush)(w) >> 8));
  s.pending_buf[s.pending++] = (w) & 0xff;
  s.pending_buf[s.pending++] = (w >>> 8) & 0xff;
}


/* ===========================================================================
 * Send a value on a given number of bits.
 * IN assertion: length <= 16 and value fits in length bits.
 */
function send_bits(s, value, length) {
  if (s.bi_valid > (Buf_size - length)) {
    s.bi_buf |= (value << s.bi_valid) & 0xffff;
    put_short(s, s.bi_buf);
    s.bi_buf = value >> (Buf_size - s.bi_valid);
    s.bi_valid += length - Buf_size;
  } else {
    s.bi_buf |= (value << s.bi_valid) & 0xffff;
    s.bi_valid += length;
  }
}


function send_code(s, c, tree) {
  send_bits(s, tree[c * 2]/*.Code*/, tree[c * 2 + 1]/*.Len*/);
}


/* ===========================================================================
 * Reverse the first len bits of a code, using straightforward code (a faster
 * method would use a table)
 * IN assertion: 1 <= len <= 15
 */
function bi_reverse(code, len) {
  var res = 0;
  do {
    res |= code & 1;
    code >>>= 1;
    res <<= 1;
  } while (--len > 0);
  return res >>> 1;
}


/* ===========================================================================
 * Flush the bit buffer, keeping at most 7 bits in it.
 */
function bi_flush(s) {
  if (s.bi_valid === 16) {
    put_short(s, s.bi_buf);
    s.bi_buf = 0;
    s.bi_valid = 0;

  } else if (s.bi_valid >= 8) {
    s.pending_buf[s.pending++] = s.bi_buf & 0xff;
    s.bi_buf >>= 8;
    s.bi_valid -= 8;
  }
}


/* ===========================================================================
 * Compute the optimal bit lengths for a tree and update the total bit length
 * for the current block.
 * IN assertion: the fields freq and dad are set, heap[heap_max] and
 *    above are the tree nodes sorted by increasing frequency.
 * OUT assertions: the field len is set to the optimal bit length, the
 *     array bl_count contains the frequencies for each bit length.
 *     The length opt_len is updated; static_len is also updated if stree is
 *     not null.
 */
function gen_bitlen(s, desc)
//    deflate_state *s;
//    tree_desc *desc;    /* the tree descriptor */
{
  var tree            = desc.dyn_tree;
  var max_code        = desc.max_code;
  var stree           = desc.stat_desc.static_tree;
  var has_stree       = desc.stat_desc.has_stree;
  var extra           = desc.stat_desc.extra_bits;
  var base            = desc.stat_desc.extra_base;
  var max_length      = desc.stat_desc.max_length;
  var h;              /* heap index */
  var n, m;           /* iterate over the tree elements */
  var bits;           /* bit length */
  var xbits;          /* extra bits */
  var f;              /* frequency */
  var overflow = 0;   /* number of elements with bit length too large */

  for (bits = 0; bits <= MAX_BITS; bits++) {
    s.bl_count[bits] = 0;
  }

  /* In a first pass, compute the optimal bit lengths (which may
   * overflow in the case of the bit length tree).
   */
  tree[s.heap[s.heap_max] * 2 + 1]/*.Len*/ = 0; /* root of the heap */

  for (h = s.heap_max + 1; h < HEAP_SIZE; h++) {
    n = s.heap[h];
    bits = tree[tree[n * 2 + 1]/*.Dad*/ * 2 + 1]/*.Len*/ + 1;
    if (bits > max_length) {
      bits = max_length;
      overflow++;
    }
    tree[n * 2 + 1]/*.Len*/ = bits;
    /* We overwrite tree[n].Dad which is no longer needed */

    if (n > max_code) { continue; } /* not a leaf node */

    s.bl_count[bits]++;
    xbits = 0;
    if (n >= base) {
      xbits = extra[n - base];
    }
    f = tree[n * 2]/*.Freq*/;
    s.opt_len += f * (bits + xbits);
    if (has_stree) {
      s.static_len += f * (stree[n * 2 + 1]/*.Len*/ + xbits);
    }
  }
  if (overflow === 0) { return; }

  // Trace((stderr,"\nbit length overflow\n"));
  /* This happens for example on obj2 and pic of the Calgary corpus */

  /* Find the first bit length which could increase: */
  do {
    bits = max_length - 1;
    while (s.bl_count[bits] === 0) { bits--; }
    s.bl_count[bits]--;      /* move one leaf down the tree */
    s.bl_count[bits + 1] += 2; /* move one overflow item as its brother */
    s.bl_count[max_length]--;
    /* The brother of the overflow item also moves one step up,
     * but this does not affect bl_count[max_length]
     */
    overflow -= 2;
  } while (overflow > 0);

  /* Now recompute all bit lengths, scanning in increasing frequency.
   * h is still equal to HEAP_SIZE. (It is simpler to reconstruct all
   * lengths instead of fixing only the wrong ones. This idea is taken
   * from 'ar' written by Haruhiko Okumura.)
   */
  for (bits = max_length; bits !== 0; bits--) {
    n = s.bl_count[bits];
    while (n !== 0) {
      m = s.heap[--h];
      if (m > max_code) { continue; }
      if (tree[m * 2 + 1]/*.Len*/ !== bits) {
        // Trace((stderr,"code %d bits %d->%d\n", m, tree[m].Len, bits));
        s.opt_len += (bits - tree[m * 2 + 1]/*.Len*/) * tree[m * 2]/*.Freq*/;
        tree[m * 2 + 1]/*.Len*/ = bits;
      }
      n--;
    }
  }
}


/* ===========================================================================
 * Generate the codes for a given tree and bit counts (which need not be
 * optimal).
 * IN assertion: the array bl_count contains the bit length statistics for
 * the given tree and the field len is set for all tree elements.
 * OUT assertion: the field code is set for all tree elements of non
 *     zero code length.
 */
function gen_codes(tree, max_code, bl_count)
//    ct_data *tree;             /* the tree to decorate */
//    int max_code;              /* largest code with non zero frequency */
//    ushf *bl_count;            /* number of codes at each bit length */
{
  var next_code = new Array(MAX_BITS + 1); /* next code value for each bit length */
  var code = 0;              /* running code value */
  var bits;                  /* bit index */
  var n;                     /* code index */

  /* The distribution counts are first used to generate the code values
   * without bit reversal.
   */
  for (bits = 1; bits <= MAX_BITS; bits++) {
    next_code[bits] = code = (code + bl_count[bits - 1]) << 1;
  }
  /* Check that the bit counts in bl_count are consistent. The last code
   * must be all ones.
   */
  //Assert (code + bl_count[MAX_BITS]-1 == (1<<MAX_BITS)-1,
  //        "inconsistent bit counts");
  //Tracev((stderr,"\ngen_codes: max_code %d ", max_code));

  for (n = 0;  n <= max_code; n++) {
    var len = tree[n * 2 + 1]/*.Len*/;
    if (len === 0) { continue; }
    /* Now reverse the bits */
    tree[n * 2]/*.Code*/ = bi_reverse(next_code[len]++, len);

    //Tracecv(tree != static_ltree, (stderr,"\nn %3d %c l %2d c %4x (%x) ",
    //     n, (isgraph(n) ? n : ' '), len, tree[n].Code, next_code[len]-1));
  }
}


/* ===========================================================================
 * Initialize the various 'constant' tables.
 */
function tr_static_init() {
  var n;        /* iterates over tree elements */
  var bits;     /* bit counter */
  var length;   /* length value */
  var code;     /* code value */
  var dist;     /* distance index */
  var bl_count = new Array(MAX_BITS + 1);
  /* number of codes at each bit length for an optimal tree */

  // do check in _tr_init()
  //if (static_init_done) return;

  /* For some embedded targets, global variables are not initialized: */
/*#ifdef NO_INIT_GLOBAL_POINTERS
  static_l_desc.static_tree = static_ltree;
  static_l_desc.extra_bits = extra_lbits;
  static_d_desc.static_tree = static_dtree;
  static_d_desc.extra_bits = extra_dbits;
  static_bl_desc.extra_bits = extra_blbits;
#endif*/

  /* Initialize the mapping length (0..255) -> length code (0..28) */
  length = 0;
  for (code = 0; code < LENGTH_CODES - 1; code++) {
    base_length[code] = length;
    for (n = 0; n < (1 << extra_lbits[code]); n++) {
      _length_code[length++] = code;
    }
  }
  //Assert (length == 256, "tr_static_init: length != 256");
  /* Note that the length 255 (match length 258) can be represented
   * in two different ways: code 284 + 5 bits or code 285, so we
   * overwrite length_code[255] to use the best encoding:
   */
  _length_code[length - 1] = code;

  /* Initialize the mapping dist (0..32K) -> dist code (0..29) */
  dist = 0;
  for (code = 0; code < 16; code++) {
    base_dist[code] = dist;
    for (n = 0; n < (1 << extra_dbits[code]); n++) {
      _dist_code[dist++] = code;
    }
  }
  //Assert (dist == 256, "tr_static_init: dist != 256");
  dist >>= 7; /* from now on, all distances are divided by 128 */
  for (; code < D_CODES; code++) {
    base_dist[code] = dist << 7;
    for (n = 0; n < (1 << (extra_dbits[code] - 7)); n++) {
      _dist_code[256 + dist++] = code;
    }
  }
  //Assert (dist == 256, "tr_static_init: 256+dist != 512");

  /* Construct the codes of the static literal tree */
  for (bits = 0; bits <= MAX_BITS; bits++) {
    bl_count[bits] = 0;
  }

  n = 0;
  while (n <= 143) {
    static_ltree[n * 2 + 1]/*.Len*/ = 8;
    n++;
    bl_count[8]++;
  }
  while (n <= 255) {
    static_ltree[n * 2 + 1]/*.Len*/ = 9;
    n++;
    bl_count[9]++;
  }
  while (n <= 279) {
    static_ltree[n * 2 + 1]/*.Len*/ = 7;
    n++;
    bl_count[7]++;
  }
  while (n <= 287) {
    static_ltree[n * 2 + 1]/*.Len*/ = 8;
    n++;
    bl_count[8]++;
  }
  /* Codes 286 and 287 do not exist, but we must include them in the
   * tree construction to get a canonical Huffman tree (longest code
   * all ones)
   */
  gen_codes(static_ltree, L_CODES + 1, bl_count);

  /* The static distance tree is trivial: */
  for (n = 0; n < D_CODES; n++) {
    static_dtree[n * 2 + 1]/*.Len*/ = 5;
    static_dtree[n * 2]/*.Code*/ = bi_reverse(n, 5);
  }

  // Now data ready and we can init static trees
  static_l_desc = new StaticTreeDesc(static_ltree, extra_lbits, LITERALS + 1, L_CODES, MAX_BITS);
  static_d_desc = new StaticTreeDesc(static_dtree, extra_dbits, 0,          D_CODES, MAX_BITS);
  static_bl_desc = new StaticTreeDesc(new Array(0), extra_blbits, 0,         BL_CODES, MAX_BL_BITS);

  //static_init_done = true;
}


/* ===========================================================================
 * Initialize a new block.
 */
function init_block(s) {
  var n; /* iterates over tree elements */

  /* Initialize the trees. */
  for (n = 0; n < L_CODES;  n++) { s.dyn_ltree[n * 2]/*.Freq*/ = 0; }
  for (n = 0; n < D_CODES;  n++) { s.dyn_dtree[n * 2]/*.Freq*/ = 0; }
  for (n = 0; n < BL_CODES; n++) { s.bl_tree[n * 2]/*.Freq*/ = 0; }

  s.dyn_ltree[END_BLOCK * 2]/*.Freq*/ = 1;
  s.opt_len = s.static_len = 0;
  s.last_lit = s.matches = 0;
}


/* ===========================================================================
 * Flush the bit buffer and align the output on a byte boundary
 */
function bi_windup(s)
{
  if (s.bi_valid > 8) {
    put_short(s, s.bi_buf);
  } else if (s.bi_valid > 0) {
    //put_byte(s, (Byte)s->bi_buf);
    s.pending_buf[s.pending++] = s.bi_buf;
  }
  s.bi_buf = 0;
  s.bi_valid = 0;
}

/* ===========================================================================
 * Copy a stored block, storing first the length and its
 * one's complement if requested.
 */
function copy_block(s, buf, len, header)
//DeflateState *s;
//charf    *buf;    /* the input data */
//unsigned len;     /* its length */
//int      header;  /* true if block header must be written */
{
  bi_windup(s);        /* align on byte boundary */

  if (header) {
    put_short(s, len);
    put_short(s, ~len);
  }
//  while (len--) {
//    put_byte(s, *buf++);
//  }
  utils.arraySet(s.pending_buf, s.window, buf, len, s.pending);
  s.pending += len;
}

/* ===========================================================================
 * Compares to subtrees, using the tree depth as tie breaker when
 * the subtrees have equal frequency. This minimizes the worst case length.
 */
function smaller(tree, n, m, depth) {
  var _n2 = n * 2;
  var _m2 = m * 2;
  return (tree[_n2]/*.Freq*/ < tree[_m2]/*.Freq*/ ||
         (tree[_n2]/*.Freq*/ === tree[_m2]/*.Freq*/ && depth[n] <= depth[m]));
}

/* ===========================================================================
 * Restore the heap property by moving down the tree starting at node k,
 * exchanging a node with the smallest of its two sons if necessary, stopping
 * when the heap property is re-established (each father smaller than its
 * two sons).
 */
function pqdownheap(s, tree, k)
//    deflate_state *s;
//    ct_data *tree;  /* the tree to restore */
//    int k;               /* node to move down */
{
  var v = s.heap[k];
  var j = k << 1;  /* left son of k */
  while (j <= s.heap_len) {
    /* Set j to the smallest of the two sons: */
    if (j < s.heap_len &&
      smaller(tree, s.heap[j + 1], s.heap[j], s.depth)) {
      j++;
    }
    /* Exit if v is smaller than both sons */
    if (smaller(tree, v, s.heap[j], s.depth)) { break; }

    /* Exchange v with the smallest son */
    s.heap[k] = s.heap[j];
    k = j;

    /* And continue down the tree, setting j to the left son of k */
    j <<= 1;
  }
  s.heap[k] = v;
}


// inlined manually
// var SMALLEST = 1;

/* ===========================================================================
 * Send the block data compressed using the given Huffman trees
 */
function compress_block(s, ltree, dtree)
//    deflate_state *s;
//    const ct_data *ltree; /* literal tree */
//    const ct_data *dtree; /* distance tree */
{
  var dist;           /* distance of matched string */
  var lc;             /* match length or unmatched char (if dist == 0) */
  var lx = 0;         /* running index in l_buf */
  var code;           /* the code to send */
  var extra;          /* number of extra bits to send */

  if (s.last_lit !== 0) {
    do {
      dist = (s.pending_buf[s.d_buf + lx * 2] << 8) | (s.pending_buf[s.d_buf + lx * 2 + 1]);
      lc = s.pending_buf[s.l_buf + lx];
      lx++;

      if (dist === 0) {
        send_code(s, lc, ltree); /* send a literal byte */
        //Tracecv(isgraph(lc), (stderr," '%c' ", lc));
      } else {
        /* Here, lc is the match length - MIN_MATCH */
        code = _length_code[lc];
        send_code(s, code + LITERALS + 1, ltree); /* send the length code */
        extra = extra_lbits[code];
        if (extra !== 0) {
          lc -= base_length[code];
          send_bits(s, lc, extra);       /* send the extra length bits */
        }
        dist--; /* dist is now the match distance - 1 */
        code = d_code(dist);
        //Assert (code < D_CODES, "bad d_code");

        send_code(s, code, dtree);       /* send the distance code */
        extra = extra_dbits[code];
        if (extra !== 0) {
          dist -= base_dist[code];
          send_bits(s, dist, extra);   /* send the extra distance bits */
        }
      } /* literal or match pair ? */

      /* Check that the overlay between pending_buf and d_buf+l_buf is ok: */
      //Assert((uInt)(s->pending) < s->lit_bufsize + 2*lx,
      //       "pendingBuf overflow");

    } while (lx < s.last_lit);
  }

  send_code(s, END_BLOCK, ltree);
}


/* ===========================================================================
 * Construct one Huffman tree and assigns the code bit strings and lengths.
 * Update the total bit length for the current block.
 * IN assertion: the field freq is set for all tree elements.
 * OUT assertions: the fields len and code are set to the optimal bit length
 *     and corresponding code. The length opt_len is updated; static_len is
 *     also updated if stree is not null. The field max_code is set.
 */
function build_tree(s, desc)
//    deflate_state *s;
//    tree_desc *desc; /* the tree descriptor */
{
  var tree     = desc.dyn_tree;
  var stree    = desc.stat_desc.static_tree;
  var has_stree = desc.stat_desc.has_stree;
  var elems    = desc.stat_desc.elems;
  var n, m;          /* iterate over heap elements */
  var max_code = -1; /* largest code with non zero frequency */
  var node;          /* new node being created */

  /* Construct the initial heap, with least frequent element in
   * heap[SMALLEST]. The sons of heap[n] are heap[2*n] and heap[2*n+1].
   * heap[0] is not used.
   */
  s.heap_len = 0;
  s.heap_max = HEAP_SIZE;

  for (n = 0; n < elems; n++) {
    if (tree[n * 2]/*.Freq*/ !== 0) {
      s.heap[++s.heap_len] = max_code = n;
      s.depth[n] = 0;

    } else {
      tree[n * 2 + 1]/*.Len*/ = 0;
    }
  }

  /* The pkzip format requires that at least one distance code exists,
   * and that at least one bit should be sent even if there is only one
   * possible code. So to avoid special checks later on we force at least
   * two codes of non zero frequency.
   */
  while (s.heap_len < 2) {
    node = s.heap[++s.heap_len] = (max_code < 2 ? ++max_code : 0);
    tree[node * 2]/*.Freq*/ = 1;
    s.depth[node] = 0;
    s.opt_len--;

    if (has_stree) {
      s.static_len -= stree[node * 2 + 1]/*.Len*/;
    }
    /* node is 0 or 1 so it does not have extra bits */
  }
  desc.max_code = max_code;

  /* The elements heap[heap_len/2+1 .. heap_len] are leaves of the tree,
   * establish sub-heaps of increasing lengths:
   */
  for (n = (s.heap_len >> 1/*int /2*/); n >= 1; n--) { pqdownheap(s, tree, n); }

  /* Construct the Huffman tree by repeatedly combining the least two
   * frequent nodes.
   */
  node = elems;              /* next internal node of the tree */
  do {
    //pqremove(s, tree, n);  /* n = node of least frequency */
    /*** pqremove ***/
    n = s.heap[1/*SMALLEST*/];
    s.heap[1/*SMALLEST*/] = s.heap[s.heap_len--];
    pqdownheap(s, tree, 1/*SMALLEST*/);
    /***/

    m = s.heap[1/*SMALLEST*/]; /* m = node of next least frequency */

    s.heap[--s.heap_max] = n; /* keep the nodes sorted by frequency */
    s.heap[--s.heap_max] = m;

    /* Create a new node father of n and m */
    tree[node * 2]/*.Freq*/ = tree[n * 2]/*.Freq*/ + tree[m * 2]/*.Freq*/;
    s.depth[node] = (s.depth[n] >= s.depth[m] ? s.depth[n] : s.depth[m]) + 1;
    tree[n * 2 + 1]/*.Dad*/ = tree[m * 2 + 1]/*.Dad*/ = node;

    /* and insert the new node in the heap */
    s.heap[1/*SMALLEST*/] = node++;
    pqdownheap(s, tree, 1/*SMALLEST*/);

  } while (s.heap_len >= 2);

  s.heap[--s.heap_max] = s.heap[1/*SMALLEST*/];

  /* At this point, the fields freq and dad are set. We can now
   * generate the bit lengths.
   */
  gen_bitlen(s, desc);

  /* The field len is now set, we can generate the bit codes */
  gen_codes(tree, max_code, s.bl_count);
}


/* ===========================================================================
 * Scan a literal or distance tree to determine the frequencies of the codes
 * in the bit length tree.
 */
function scan_tree(s, tree, max_code)
//    deflate_state *s;
//    ct_data *tree;   /* the tree to be scanned */
//    int max_code;    /* and its largest code of non zero frequency */
{
  var n;                     /* iterates over all tree elements */
  var prevlen = -1;          /* last emitted length */
  var curlen;                /* length of current code */

  var nextlen = tree[0 * 2 + 1]/*.Len*/; /* length of next code */

  var count = 0;             /* repeat count of the current code */
  var max_count = 7;         /* max repeat count */
  var min_count = 4;         /* min repeat count */

  if (nextlen === 0) {
    max_count = 138;
    min_count = 3;
  }
  tree[(max_code + 1) * 2 + 1]/*.Len*/ = 0xffff; /* guard */

  for (n = 0; n <= max_code; n++) {
    curlen = nextlen;
    nextlen = tree[(n + 1) * 2 + 1]/*.Len*/;

    if (++count < max_count && curlen === nextlen) {
      continue;

    } else if (count < min_count) {
      s.bl_tree[curlen * 2]/*.Freq*/ += count;

    } else if (curlen !== 0) {

      if (curlen !== prevlen) { s.bl_tree[curlen * 2]/*.Freq*/++; }
      s.bl_tree[REP_3_6 * 2]/*.Freq*/++;

    } else if (count <= 10) {
      s.bl_tree[REPZ_3_10 * 2]/*.Freq*/++;

    } else {
      s.bl_tree[REPZ_11_138 * 2]/*.Freq*/++;
    }

    count = 0;
    prevlen = curlen;

    if (nextlen === 0) {
      max_count = 138;
      min_count = 3;

    } else if (curlen === nextlen) {
      max_count = 6;
      min_count = 3;

    } else {
      max_count = 7;
      min_count = 4;
    }
  }
}


/* ===========================================================================
 * Send a literal or distance tree in compressed form, using the codes in
 * bl_tree.
 */
function send_tree(s, tree, max_code)
//    deflate_state *s;
//    ct_data *tree; /* the tree to be scanned */
//    int max_code;       /* and its largest code of non zero frequency */
{
  var n;                     /* iterates over all tree elements */
  var prevlen = -1;          /* last emitted length */
  var curlen;                /* length of current code */

  var nextlen = tree[0 * 2 + 1]/*.Len*/; /* length of next code */

  var count = 0;             /* repeat count of the current code */
  var max_count = 7;         /* max repeat count */
  var min_count = 4;         /* min repeat count */

  /* tree[max_code+1].Len = -1; */  /* guard already set */
  if (nextlen === 0) {
    max_count = 138;
    min_count = 3;
  }

  for (n = 0; n <= max_code; n++) {
    curlen = nextlen;
    nextlen = tree[(n + 1) * 2 + 1]/*.Len*/;

    if (++count < max_count && curlen === nextlen) {
      continue;

    } else if (count < min_count) {
      do { send_code(s, curlen, s.bl_tree); } while (--count !== 0);

    } else if (curlen !== 0) {
      if (curlen !== prevlen) {
        send_code(s, curlen, s.bl_tree);
        count--;
      }
      //Assert(count >= 3 && count <= 6, " 3_6?");
      send_code(s, REP_3_6, s.bl_tree);
      send_bits(s, count - 3, 2);

    } else if (count <= 10) {
      send_code(s, REPZ_3_10, s.bl_tree);
      send_bits(s, count - 3, 3);

    } else {
      send_code(s, REPZ_11_138, s.bl_tree);
      send_bits(s, count - 11, 7);
    }

    count = 0;
    prevlen = curlen;
    if (nextlen === 0) {
      max_count = 138;
      min_count = 3;

    } else if (curlen === nextlen) {
      max_count = 6;
      min_count = 3;

    } else {
      max_count = 7;
      min_count = 4;
    }
  }
}


/* ===========================================================================
 * Construct the Huffman tree for the bit lengths and return the index in
 * bl_order of the last bit length code to send.
 */
function build_bl_tree(s) {
  var max_blindex;  /* index of last bit length code of non zero freq */

  /* Determine the bit length frequencies for literal and distance trees */
  scan_tree(s, s.dyn_ltree, s.l_desc.max_code);
  scan_tree(s, s.dyn_dtree, s.d_desc.max_code);

  /* Build the bit length tree: */
  build_tree(s, s.bl_desc);
  /* opt_len now includes the length of the tree representations, except
   * the lengths of the bit lengths codes and the 5+5+4 bits for the counts.
   */

  /* Determine the number of bit length codes to send. The pkzip format
   * requires that at least 4 bit length codes be sent. (appnote.txt says
   * 3 but the actual value used is 4.)
   */
  for (max_blindex = BL_CODES - 1; max_blindex >= 3; max_blindex--) {
    if (s.bl_tree[bl_order[max_blindex] * 2 + 1]/*.Len*/ !== 0) {
      break;
    }
  }
  /* Update opt_len to include the bit length tree and counts */
  s.opt_len += 3 * (max_blindex + 1) + 5 + 5 + 4;
  //Tracev((stderr, "\ndyn trees: dyn %ld, stat %ld",
  //        s->opt_len, s->static_len));

  return max_blindex;
}


/* ===========================================================================
 * Send the header for a block using dynamic Huffman trees: the counts, the
 * lengths of the bit length codes, the literal tree and the distance tree.
 * IN assertion: lcodes >= 257, dcodes >= 1, blcodes >= 4.
 */
function send_all_trees(s, lcodes, dcodes, blcodes)
//    deflate_state *s;
//    int lcodes, dcodes, blcodes; /* number of codes for each tree */
{
  var rank;                    /* index in bl_order */

  //Assert (lcodes >= 257 && dcodes >= 1 && blcodes >= 4, "not enough codes");
  //Assert (lcodes <= L_CODES && dcodes <= D_CODES && blcodes <= BL_CODES,
  //        "too many codes");
  //Tracev((stderr, "\nbl counts: "));
  send_bits(s, lcodes - 257, 5); /* not +255 as stated in appnote.txt */
  send_bits(s, dcodes - 1,   5);
  send_bits(s, blcodes - 4,  4); /* not -3 as stated in appnote.txt */
  for (rank = 0; rank < blcodes; rank++) {
    //Tracev((stderr, "\nbl code %2d ", bl_order[rank]));
    send_bits(s, s.bl_tree[bl_order[rank] * 2 + 1]/*.Len*/, 3);
  }
  //Tracev((stderr, "\nbl tree: sent %ld", s->bits_sent));

  send_tree(s, s.dyn_ltree, lcodes - 1); /* literal tree */
  //Tracev((stderr, "\nlit tree: sent %ld", s->bits_sent));

  send_tree(s, s.dyn_dtree, dcodes - 1); /* distance tree */
  //Tracev((stderr, "\ndist tree: sent %ld", s->bits_sent));
}


/* ===========================================================================
 * Check if the data type is TEXT or BINARY, using the following algorithm:
 * - TEXT if the two conditions below are satisfied:
 *    a) There are no non-portable control characters belonging to the
 *       "black list" (0..6, 14..25, 28..31).
 *    b) There is at least one printable character belonging to the
 *       "white list" (9 {TAB}, 10 {LF}, 13 {CR}, 32..255).
 * - BINARY otherwise.
 * - The following partially-portable control characters form a
 *   "gray list" that is ignored in this detection algorithm:
 *   (7 {BEL}, 8 {BS}, 11 {VT}, 12 {FF}, 26 {SUB}, 27 {ESC}).
 * IN assertion: the fields Freq of dyn_ltree are set.
 */
function detect_data_type(s) {
  /* black_mask is the bit mask of black-listed bytes
   * set bits 0..6, 14..25, and 28..31
   * 0xf3ffc07f = binary 11110011111111111100000001111111
   */
  var black_mask = 0xf3ffc07f;
  var n;

  /* Check for non-textual ("black-listed") bytes. */
  for (n = 0; n <= 31; n++, black_mask >>>= 1) {
    if ((black_mask & 1) && (s.dyn_ltree[n * 2]/*.Freq*/ !== 0)) {
      return Z_BINARY;
    }
  }

  /* Check for textual ("white-listed") bytes. */
  if (s.dyn_ltree[9 * 2]/*.Freq*/ !== 0 || s.dyn_ltree[10 * 2]/*.Freq*/ !== 0 ||
      s.dyn_ltree[13 * 2]/*.Freq*/ !== 0) {
    return Z_TEXT;
  }
  for (n = 32; n < LITERALS; n++) {
    if (s.dyn_ltree[n * 2]/*.Freq*/ !== 0) {
      return Z_TEXT;
    }
  }

  /* There are no "black-listed" or "white-listed" bytes:
   * this stream either is empty or has tolerated ("gray-listed") bytes only.
   */
  return Z_BINARY;
}


var static_init_done = false;

/* ===========================================================================
 * Initialize the tree data structures for a new zlib stream.
 */
function _tr_init(s)
{

  if (!static_init_done) {
    tr_static_init();
    static_init_done = true;
  }

  s.l_desc  = new TreeDesc(s.dyn_ltree, static_l_desc);
  s.d_desc  = new TreeDesc(s.dyn_dtree, static_d_desc);
  s.bl_desc = new TreeDesc(s.bl_tree, static_bl_desc);

  s.bi_buf = 0;
  s.bi_valid = 0;

  /* Initialize the first block of the first file: */
  init_block(s);
}


/* ===========================================================================
 * Send a stored block
 */
function _tr_stored_block(s, buf, stored_len, last)
//DeflateState *s;
//charf *buf;       /* input block */
//ulg stored_len;   /* length of input block */
//int last;         /* one if this is the last block for a file */
{
  send_bits(s, (STORED_BLOCK << 1) + (last ? 1 : 0), 3);    /* send block type */
  copy_block(s, buf, stored_len, true); /* with header */
}


/* ===========================================================================
 * Send one empty static block to give enough lookahead for inflate.
 * This takes 10 bits, of which 7 may remain in the bit buffer.
 */
function _tr_align(s) {
  send_bits(s, STATIC_TREES << 1, 3);
  send_code(s, END_BLOCK, static_ltree);
  bi_flush(s);
}


/* ===========================================================================
 * Determine the best encoding for the current block: dynamic trees, static
 * trees or store, and output the encoded block to the zip file.
 */
function _tr_flush_block(s, buf, stored_len, last)
//DeflateState *s;
//charf *buf;       /* input block, or NULL if too old */
//ulg stored_len;   /* length of input block */
//int last;         /* one if this is the last block for a file */
{
  var opt_lenb, static_lenb;  /* opt_len and static_len in bytes */
  var max_blindex = 0;        /* index of last bit length code of non zero freq */

  /* Build the Huffman trees unless a stored block is forced */
  if (s.level > 0) {

    /* Check if the file is binary or text */
    if (s.strm.data_type === Z_UNKNOWN) {
      s.strm.data_type = detect_data_type(s);
    }

    /* Construct the literal and distance trees */
    build_tree(s, s.l_desc);
    // Tracev((stderr, "\nlit data: dyn %ld, stat %ld", s->opt_len,
    //        s->static_len));

    build_tree(s, s.d_desc);
    // Tracev((stderr, "\ndist data: dyn %ld, stat %ld", s->opt_len,
    //        s->static_len));
    /* At this point, opt_len and static_len are the total bit lengths of
     * the compressed block data, excluding the tree representations.
     */

    /* Build the bit length tree for the above two trees, and get the index
     * in bl_order of the last bit length code to send.
     */
    max_blindex = build_bl_tree(s);

    /* Determine the best encoding. Compute the block lengths in bytes. */
    opt_lenb = (s.opt_len + 3 + 7) >>> 3;
    static_lenb = (s.static_len + 3 + 7) >>> 3;

    // Tracev((stderr, "\nopt %lu(%lu) stat %lu(%lu) stored %lu lit %u ",
    //        opt_lenb, s->opt_len, static_lenb, s->static_len, stored_len,
    //        s->last_lit));

    if (static_lenb <= opt_lenb) { opt_lenb = static_lenb; }

  } else {
    // Assert(buf != (char*)0, "lost buf");
    opt_lenb = static_lenb = stored_len + 5; /* force a stored block */
  }

  if ((stored_len + 4 <= opt_lenb) && (buf !== -1)) {
    /* 4: two words for the lengths */

    /* The test buf != NULL is only necessary if LIT_BUFSIZE > WSIZE.
     * Otherwise we can't have processed more than WSIZE input bytes since
     * the last block flush, because compression would have been
     * successful. If LIT_BUFSIZE <= WSIZE, it is never too late to
     * transform a block into a stored block.
     */
    _tr_stored_block(s, buf, stored_len, last);

  } else if (s.strategy === Z_FIXED || static_lenb === opt_lenb) {

    send_bits(s, (STATIC_TREES << 1) + (last ? 1 : 0), 3);
    compress_block(s, static_ltree, static_dtree);

  } else {
    send_bits(s, (DYN_TREES << 1) + (last ? 1 : 0), 3);
    send_all_trees(s, s.l_desc.max_code + 1, s.d_desc.max_code + 1, max_blindex + 1);
    compress_block(s, s.dyn_ltree, s.dyn_dtree);
  }
  // Assert (s->compressed_len == s->bits_sent, "bad compressed size");
  /* The above check is made mod 2^32, for files larger than 512 MB
   * and uLong implemented on 32 bits.
   */
  init_block(s);

  if (last) {
    bi_windup(s);
  }
  // Tracev((stderr,"\ncomprlen %lu(%lu) ", s->compressed_len>>3,
  //       s->compressed_len-7*last));
}

/* ===========================================================================
 * Save the match info and tally the frequency counts. Return true if
 * the current block must be flushed.
 */
function _tr_tally(s, dist, lc)
//    deflate_state *s;
//    unsigned dist;  /* distance of matched string */
//    unsigned lc;    /* match length-MIN_MATCH or unmatched char (if dist==0) */
{
  //var out_length, in_length, dcode;

  s.pending_buf[s.d_buf + s.last_lit * 2]     = (dist >>> 8) & 0xff;
  s.pending_buf[s.d_buf + s.last_lit * 2 + 1] = dist & 0xff;

  s.pending_buf[s.l_buf + s.last_lit] = lc & 0xff;
  s.last_lit++;

  if (dist === 0) {
    /* lc is the unmatched char */
    s.dyn_ltree[lc * 2]/*.Freq*/++;
  } else {
    s.matches++;
    /* Here, lc is the match length - MIN_MATCH */
    dist--;             /* dist = match distance - 1 */
    //Assert((ush)dist < (ush)MAX_DIST(s) &&
    //       (ush)lc <= (ush)(MAX_MATCH-MIN_MATCH) &&
    //       (ush)d_code(dist) < (ush)D_CODES,  "_tr_tally: bad match");

    s.dyn_ltree[(_length_code[lc] + LITERALS + 1) * 2]/*.Freq*/++;
    s.dyn_dtree[d_code(dist) * 2]/*.Freq*/++;
  }

// (!) This block is disabled in zlib defaults,
// don't enable it for binary compatibility

//#ifdef TRUNCATE_BLOCK
//  /* Try to guess if it is profitable to stop the current block here */
//  if ((s.last_lit & 0x1fff) === 0 && s.level > 2) {
//    /* Compute an upper bound for the compressed length */
//    out_length = s.last_lit*8;
//    in_length = s.strstart - s.block_start;
//
//    for (dcode = 0; dcode < D_CODES; dcode++) {
//      out_length += s.dyn_dtree[dcode*2]/*.Freq*/ * (5 + extra_dbits[dcode]);
//    }
//    out_length >>>= 3;
//    //Tracev((stderr,"\nlast_lit %u, in %ld, out ~%ld(%ld%%) ",
//    //       s->last_lit, in_length, out_length,
//    //       100L - out_length*100L/in_length));
//    if (s.matches < (s.last_lit>>1)/*int /2*/ && out_length < (in_length>>1)/*int /2*/) {
//      return true;
//    }
//  }
//#endif

  return (s.last_lit === s.lit_bufsize - 1);
  /* We avoid equality with lit_bufsize because of wraparound at 64K
   * on 16 bit machines and because stored blocks are restricted to
   * 64K-1 bytes.
   */
}

exports._tr_init  = _tr_init;
exports._tr_stored_block = _tr_stored_block;
exports._tr_flush_block  = _tr_flush_block;
exports._tr_tally = _tr_tally;
exports._tr_align = _tr_align;


/***/ }),
/* 357 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

module.exports = {
  2:      'need dictionary',     /* Z_NEED_DICT       2  */
  1:      'stream end',          /* Z_STREAM_END      1  */
  0:      '',                    /* Z_OK              0  */
  '-1':   'file error',          /* Z_ERRNO         (-1) */
  '-2':   'stream error',        /* Z_STREAM_ERROR  (-2) */
  '-3':   'data error',          /* Z_DATA_ERROR    (-3) */
  '-4':   'insufficient memory', /* Z_MEM_ERROR     (-4) */
  '-5':   'buffer error',        /* Z_BUF_ERROR     (-5) */
  '-6':   'incompatible version' /* Z_VERSION_ERROR (-6) */
};


/***/ }),
/* 358 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

var utils         = __webpack_require__(126);
var adler32       = __webpack_require__(246);
var crc32         = __webpack_require__(247);
var inflate_fast  = __webpack_require__(359);
var inflate_table = __webpack_require__(360);

var CODES = 0;
var LENS = 1;
var DISTS = 2;

/* Public constants ==========================================================*/
/* ===========================================================================*/


/* Allowed flush values; see deflate() and inflate() below for details */
//var Z_NO_FLUSH      = 0;
//var Z_PARTIAL_FLUSH = 1;
//var Z_SYNC_FLUSH    = 2;
//var Z_FULL_FLUSH    = 3;
var Z_FINISH        = 4;
var Z_BLOCK         = 5;
var Z_TREES         = 6;


/* Return codes for the compression/decompression functions. Negative values
 * are errors, positive values are used for special but normal events.
 */
var Z_OK            = 0;
var Z_STREAM_END    = 1;
var Z_NEED_DICT     = 2;
//var Z_ERRNO         = -1;
var Z_STREAM_ERROR  = -2;
var Z_DATA_ERROR    = -3;
var Z_MEM_ERROR     = -4;
var Z_BUF_ERROR     = -5;
//var Z_VERSION_ERROR = -6;

/* The deflate compression method */
var Z_DEFLATED  = 8;


/* STATES ====================================================================*/
/* ===========================================================================*/


var    HEAD = 1;       /* i: waiting for magic header */
var    FLAGS = 2;      /* i: waiting for method and flags (gzip) */
var    TIME = 3;       /* i: waiting for modification time (gzip) */
var    OS = 4;         /* i: waiting for extra flags and operating system (gzip) */
var    EXLEN = 5;      /* i: waiting for extra length (gzip) */
var    EXTRA = 6;      /* i: waiting for extra bytes (gzip) */
var    NAME = 7;       /* i: waiting for end of file name (gzip) */
var    COMMENT = 8;    /* i: waiting for end of comment (gzip) */
var    HCRC = 9;       /* i: waiting for header crc (gzip) */
var    DICTID = 10;    /* i: waiting for dictionary check value */
var    DICT = 11;      /* waiting for inflateSetDictionary() call */
var        TYPE = 12;      /* i: waiting for type bits, including last-flag bit */
var        TYPEDO = 13;    /* i: same, but skip check to exit inflate on new block */
var        STORED = 14;    /* i: waiting for stored size (length and complement) */
var        COPY_ = 15;     /* i/o: same as COPY below, but only first time in */
var        COPY = 16;      /* i/o: waiting for input or output to copy stored block */
var        TABLE = 17;     /* i: waiting for dynamic block table lengths */
var        LENLENS = 18;   /* i: waiting for code length code lengths */
var        CODELENS = 19;  /* i: waiting for length/lit and distance code lengths */
var            LEN_ = 20;      /* i: same as LEN below, but only first time in */
var            LEN = 21;       /* i: waiting for length/lit/eob code */
var            LENEXT = 22;    /* i: waiting for length extra bits */
var            DIST = 23;      /* i: waiting for distance code */
var            DISTEXT = 24;   /* i: waiting for distance extra bits */
var            MATCH = 25;     /* o: waiting for output space to copy string */
var            LIT = 26;       /* o: waiting for output space to write literal */
var    CHECK = 27;     /* i: waiting for 32-bit check value */
var    LENGTH = 28;    /* i: waiting for 32-bit length (gzip) */
var    DONE = 29;      /* finished check, done -- remain here until reset */
var    BAD = 30;       /* got a data error -- remain here until reset */
var    MEM = 31;       /* got an inflate() memory error -- remain here until reset */
var    SYNC = 32;      /* looking for synchronization bytes to restart inflate() */

/* ===========================================================================*/



var ENOUGH_LENS = 852;
var ENOUGH_DISTS = 592;
//var ENOUGH =  (ENOUGH_LENS+ENOUGH_DISTS);

var MAX_WBITS = 15;
/* 32K LZ77 window */
var DEF_WBITS = MAX_WBITS;


function zswap32(q) {
  return  (((q >>> 24) & 0xff) +
          ((q >>> 8) & 0xff00) +
          ((q & 0xff00) << 8) +
          ((q & 0xff) << 24));
}


function InflateState() {
  this.mode = 0;             /* current inflate mode */
  this.last = false;          /* true if processing last block */
  this.wrap = 0;              /* bit 0 true for zlib, bit 1 true for gzip */
  this.havedict = false;      /* true if dictionary provided */
  this.flags = 0;             /* gzip header method and flags (0 if zlib) */
  this.dmax = 0;              /* zlib header max distance (INFLATE_STRICT) */
  this.check = 0;             /* protected copy of check value */
  this.total = 0;             /* protected copy of output count */
  // TODO: may be {}
  this.head = null;           /* where to save gzip header information */

  /* sliding window */
  this.wbits = 0;             /* log base 2 of requested window size */
  this.wsize = 0;             /* window size or zero if not using window */
  this.whave = 0;             /* valid bytes in the window */
  this.wnext = 0;             /* window write index */
  this.window = null;         /* allocated sliding window, if needed */

  /* bit accumulator */
  this.hold = 0;              /* input bit accumulator */
  this.bits = 0;              /* number of bits in "in" */

  /* for string and stored block copying */
  this.length = 0;            /* literal or length of data to copy */
  this.offset = 0;            /* distance back to copy string from */

  /* for table and code decoding */
  this.extra = 0;             /* extra bits needed */

  /* fixed and dynamic code tables */
  this.lencode = null;          /* starting table for length/literal codes */
  this.distcode = null;         /* starting table for distance codes */
  this.lenbits = 0;           /* index bits for lencode */
  this.distbits = 0;          /* index bits for distcode */

  /* dynamic table building */
  this.ncode = 0;             /* number of code length code lengths */
  this.nlen = 0;              /* number of length code lengths */
  this.ndist = 0;             /* number of distance code lengths */
  this.have = 0;              /* number of code lengths in lens[] */
  this.next = null;              /* next available space in codes[] */

  this.lens = new utils.Buf16(320); /* temporary storage for code lengths */
  this.work = new utils.Buf16(288); /* work area for code table building */

  /*
   because we don't have pointers in js, we use lencode and distcode directly
   as buffers so we don't need codes
  */
  //this.codes = new utils.Buf32(ENOUGH);       /* space for code tables */
  this.lendyn = null;              /* dynamic table for length/literal codes (JS specific) */
  this.distdyn = null;             /* dynamic table for distance codes (JS specific) */
  this.sane = 0;                   /* if false, allow invalid distance too far */
  this.back = 0;                   /* bits back of last unprocessed length/lit */
  this.was = 0;                    /* initial length of match */
}

function inflateResetKeep(strm) {
  var state;

  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;
  strm.total_in = strm.total_out = state.total = 0;
  strm.msg = ''; /*Z_NULL*/
  if (state.wrap) {       /* to support ill-conceived Java test suite */
    strm.adler = state.wrap & 1;
  }
  state.mode = HEAD;
  state.last = 0;
  state.havedict = 0;
  state.dmax = 32768;
  state.head = null/*Z_NULL*/;
  state.hold = 0;
  state.bits = 0;
  //state.lencode = state.distcode = state.next = state.codes;
  state.lencode = state.lendyn = new utils.Buf32(ENOUGH_LENS);
  state.distcode = state.distdyn = new utils.Buf32(ENOUGH_DISTS);

  state.sane = 1;
  state.back = -1;
  //Tracev((stderr, "inflate: reset\n"));
  return Z_OK;
}

function inflateReset(strm) {
  var state;

  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;
  state.wsize = 0;
  state.whave = 0;
  state.wnext = 0;
  return inflateResetKeep(strm);

}

function inflateReset2(strm, windowBits) {
  var wrap;
  var state;

  /* get the state */
  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;

  /* extract wrap request from windowBits parameter */
  if (windowBits < 0) {
    wrap = 0;
    windowBits = -windowBits;
  }
  else {
    wrap = (windowBits >> 4) + 1;
    if (windowBits < 48) {
      windowBits &= 15;
    }
  }

  /* set number of window bits, free window if different */
  if (windowBits && (windowBits < 8 || windowBits > 15)) {
    return Z_STREAM_ERROR;
  }
  if (state.window !== null && state.wbits !== windowBits) {
    state.window = null;
  }

  /* update state and reset the rest of it */
  state.wrap = wrap;
  state.wbits = windowBits;
  return inflateReset(strm);
}

function inflateInit2(strm, windowBits) {
  var ret;
  var state;

  if (!strm) { return Z_STREAM_ERROR; }
  //strm.msg = Z_NULL;                 /* in case we return an error */

  state = new InflateState();

  //if (state === Z_NULL) return Z_MEM_ERROR;
  //Tracev((stderr, "inflate: allocated\n"));
  strm.state = state;
  state.window = null/*Z_NULL*/;
  ret = inflateReset2(strm, windowBits);
  if (ret !== Z_OK) {
    strm.state = null/*Z_NULL*/;
  }
  return ret;
}

function inflateInit(strm) {
  return inflateInit2(strm, DEF_WBITS);
}


/*
 Return state with length and distance decoding tables and index sizes set to
 fixed code decoding.  Normally this returns fixed tables from inffixed.h.
 If BUILDFIXED is defined, then instead this routine builds the tables the
 first time it's called, and returns those tables the first time and
 thereafter.  This reduces the size of the code by about 2K bytes, in
 exchange for a little execution time.  However, BUILDFIXED should not be
 used for threaded applications, since the rewriting of the tables and virgin
 may not be thread-safe.
 */
var virgin = true;

var lenfix, distfix; // We have no pointers in JS, so keep tables separate

function fixedtables(state) {
  /* build fixed huffman tables if first call (may not be thread safe) */
  if (virgin) {
    var sym;

    lenfix = new utils.Buf32(512);
    distfix = new utils.Buf32(32);

    /* literal/length table */
    sym = 0;
    while (sym < 144) { state.lens[sym++] = 8; }
    while (sym < 256) { state.lens[sym++] = 9; }
    while (sym < 280) { state.lens[sym++] = 7; }
    while (sym < 288) { state.lens[sym++] = 8; }

    inflate_table(LENS,  state.lens, 0, 288, lenfix,   0, state.work, { bits: 9 });

    /* distance table */
    sym = 0;
    while (sym < 32) { state.lens[sym++] = 5; }

    inflate_table(DISTS, state.lens, 0, 32,   distfix, 0, state.work, { bits: 5 });

    /* do this just once */
    virgin = false;
  }

  state.lencode = lenfix;
  state.lenbits = 9;
  state.distcode = distfix;
  state.distbits = 5;
}


/*
 Update the window with the last wsize (normally 32K) bytes written before
 returning.  If window does not exist yet, create it.  This is only called
 when a window is already in use, or when output has been written during this
 inflate call, but the end of the deflate stream has not been reached yet.
 It is also called to create a window for dictionary data when a dictionary
 is loaded.

 Providing output buffers larger than 32K to inflate() should provide a speed
 advantage, since only the last 32K of output is copied to the sliding window
 upon return from inflate(), and since all distances after the first 32K of
 output will fall in the output data, making match copies simpler and faster.
 The advantage may be dependent on the size of the processor's data caches.
 */
function updatewindow(strm, src, end, copy) {
  var dist;
  var state = strm.state;

  /* if it hasn't been done already, allocate space for the window */
  if (state.window === null) {
    state.wsize = 1 << state.wbits;
    state.wnext = 0;
    state.whave = 0;

    state.window = new utils.Buf8(state.wsize);
  }

  /* copy state->wsize or less output bytes into the circular window */
  if (copy >= state.wsize) {
    utils.arraySet(state.window, src, end - state.wsize, state.wsize, 0);
    state.wnext = 0;
    state.whave = state.wsize;
  }
  else {
    dist = state.wsize - state.wnext;
    if (dist > copy) {
      dist = copy;
    }
    //zmemcpy(state->window + state->wnext, end - copy, dist);
    utils.arraySet(state.window, src, end - copy, dist, state.wnext);
    copy -= dist;
    if (copy) {
      //zmemcpy(state->window, end - copy, copy);
      utils.arraySet(state.window, src, end - copy, copy, 0);
      state.wnext = copy;
      state.whave = state.wsize;
    }
    else {
      state.wnext += dist;
      if (state.wnext === state.wsize) { state.wnext = 0; }
      if (state.whave < state.wsize) { state.whave += dist; }
    }
  }
  return 0;
}

function inflate(strm, flush) {
  var state;
  var input, output;          // input/output buffers
  var next;                   /* next input INDEX */
  var put;                    /* next output INDEX */
  var have, left;             /* available input and output */
  var hold;                   /* bit buffer */
  var bits;                   /* bits in bit buffer */
  var _in, _out;              /* save starting available input and output */
  var copy;                   /* number of stored or match bytes to copy */
  var from;                   /* where to copy match bytes from */
  var from_source;
  var here = 0;               /* current decoding table entry */
  var here_bits, here_op, here_val; // paked "here" denormalized (JS specific)
  //var last;                   /* parent table entry */
  var last_bits, last_op, last_val; // paked "last" denormalized (JS specific)
  var len;                    /* length to copy for repeats, bits to drop */
  var ret;                    /* return code */
  var hbuf = new utils.Buf8(4);    /* buffer for gzip header crc calculation */
  var opts;

  var n; // temporary var for NEED_BITS

  var order = /* permutation of code lengths */
    [ 16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15 ];


  if (!strm || !strm.state || !strm.output ||
      (!strm.input && strm.avail_in !== 0)) {
    return Z_STREAM_ERROR;
  }

  state = strm.state;
  if (state.mode === TYPE) { state.mode = TYPEDO; }    /* skip check */


  //--- LOAD() ---
  put = strm.next_out;
  output = strm.output;
  left = strm.avail_out;
  next = strm.next_in;
  input = strm.input;
  have = strm.avail_in;
  hold = state.hold;
  bits = state.bits;
  //---

  _in = have;
  _out = left;
  ret = Z_OK;

  inf_leave: // goto emulation
  for (;;) {
    switch (state.mode) {
      case HEAD:
        if (state.wrap === 0) {
          state.mode = TYPEDO;
          break;
        }
        //=== NEEDBITS(16);
        while (bits < 16) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        if ((state.wrap & 2) && hold === 0x8b1f) {  /* gzip header */
          state.check = 0/*crc32(0L, Z_NULL, 0)*/;
          //=== CRC2(state.check, hold);
          hbuf[0] = hold & 0xff;
          hbuf[1] = (hold >>> 8) & 0xff;
          state.check = crc32(state.check, hbuf, 2, 0);
          //===//

          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          state.mode = FLAGS;
          break;
        }
        state.flags = 0;           /* expect zlib header */
        if (state.head) {
          state.head.done = false;
        }
        if (!(state.wrap & 1) ||   /* check if zlib header allowed */
          (((hold & 0xff)/*BITS(8)*/ << 8) + (hold >> 8)) % 31) {
          strm.msg = 'incorrect header check';
          state.mode = BAD;
          break;
        }
        if ((hold & 0x0f)/*BITS(4)*/ !== Z_DEFLATED) {
          strm.msg = 'unknown compression method';
          state.mode = BAD;
          break;
        }
        //--- DROPBITS(4) ---//
        hold >>>= 4;
        bits -= 4;
        //---//
        len = (hold & 0x0f)/*BITS(4)*/ + 8;
        if (state.wbits === 0) {
          state.wbits = len;
        }
        else if (len > state.wbits) {
          strm.msg = 'invalid window size';
          state.mode = BAD;
          break;
        }
        state.dmax = 1 << len;
        //Tracev((stderr, "inflate:   zlib header ok\n"));
        strm.adler = state.check = 1/*adler32(0L, Z_NULL, 0)*/;
        state.mode = hold & 0x200 ? DICTID : TYPE;
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        break;
      case FLAGS:
        //=== NEEDBITS(16); */
        while (bits < 16) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        state.flags = hold;
        if ((state.flags & 0xff) !== Z_DEFLATED) {
          strm.msg = 'unknown compression method';
          state.mode = BAD;
          break;
        }
        if (state.flags & 0xe000) {
          strm.msg = 'unknown header flags set';
          state.mode = BAD;
          break;
        }
        if (state.head) {
          state.head.text = ((hold >> 8) & 1);
        }
        if (state.flags & 0x0200) {
          //=== CRC2(state.check, hold);
          hbuf[0] = hold & 0xff;
          hbuf[1] = (hold >>> 8) & 0xff;
          state.check = crc32(state.check, hbuf, 2, 0);
          //===//
        }
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = TIME;
        /* falls through */
      case TIME:
        //=== NEEDBITS(32); */
        while (bits < 32) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        if (state.head) {
          state.head.time = hold;
        }
        if (state.flags & 0x0200) {
          //=== CRC4(state.check, hold)
          hbuf[0] = hold & 0xff;
          hbuf[1] = (hold >>> 8) & 0xff;
          hbuf[2] = (hold >>> 16) & 0xff;
          hbuf[3] = (hold >>> 24) & 0xff;
          state.check = crc32(state.check, hbuf, 4, 0);
          //===
        }
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = OS;
        /* falls through */
      case OS:
        //=== NEEDBITS(16); */
        while (bits < 16) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        if (state.head) {
          state.head.xflags = (hold & 0xff);
          state.head.os = (hold >> 8);
        }
        if (state.flags & 0x0200) {
          //=== CRC2(state.check, hold);
          hbuf[0] = hold & 0xff;
          hbuf[1] = (hold >>> 8) & 0xff;
          state.check = crc32(state.check, hbuf, 2, 0);
          //===//
        }
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = EXLEN;
        /* falls through */
      case EXLEN:
        if (state.flags & 0x0400) {
          //=== NEEDBITS(16); */
          while (bits < 16) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.length = hold;
          if (state.head) {
            state.head.extra_len = hold;
          }
          if (state.flags & 0x0200) {
            //=== CRC2(state.check, hold);
            hbuf[0] = hold & 0xff;
            hbuf[1] = (hold >>> 8) & 0xff;
            state.check = crc32(state.check, hbuf, 2, 0);
            //===//
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
        }
        else if (state.head) {
          state.head.extra = null/*Z_NULL*/;
        }
        state.mode = EXTRA;
        /* falls through */
      case EXTRA:
        if (state.flags & 0x0400) {
          copy = state.length;
          if (copy > have) { copy = have; }
          if (copy) {
            if (state.head) {
              len = state.head.extra_len - state.length;
              if (!state.head.extra) {
                // Use untyped array for more convenient processing later
                state.head.extra = new Array(state.head.extra_len);
              }
              utils.arraySet(
                state.head.extra,
                input,
                next,
                // extra field is limited to 65536 bytes
                // - no need for additional size check
                copy,
                /*len + copy > state.head.extra_max - len ? state.head.extra_max : copy,*/
                len
              );
              //zmemcpy(state.head.extra + len, next,
              //        len + copy > state.head.extra_max ?
              //        state.head.extra_max - len : copy);
            }
            if (state.flags & 0x0200) {
              state.check = crc32(state.check, input, copy, next);
            }
            have -= copy;
            next += copy;
            state.length -= copy;
          }
          if (state.length) { break inf_leave; }
        }
        state.length = 0;
        state.mode = NAME;
        /* falls through */
      case NAME:
        if (state.flags & 0x0800) {
          if (have === 0) { break inf_leave; }
          copy = 0;
          do {
            // TODO: 2 or 1 bytes?
            len = input[next + copy++];
            /* use constant limit because in js we should not preallocate memory */
            if (state.head && len &&
                (state.length < 65536 /*state.head.name_max*/)) {
              state.head.name += String.fromCharCode(len);
            }
          } while (len && copy < have);

          if (state.flags & 0x0200) {
            state.check = crc32(state.check, input, copy, next);
          }
          have -= copy;
          next += copy;
          if (len) { break inf_leave; }
        }
        else if (state.head) {
          state.head.name = null;
        }
        state.length = 0;
        state.mode = COMMENT;
        /* falls through */
      case COMMENT:
        if (state.flags & 0x1000) {
          if (have === 0) { break inf_leave; }
          copy = 0;
          do {
            len = input[next + copy++];
            /* use constant limit because in js we should not preallocate memory */
            if (state.head && len &&
                (state.length < 65536 /*state.head.comm_max*/)) {
              state.head.comment += String.fromCharCode(len);
            }
          } while (len && copy < have);
          if (state.flags & 0x0200) {
            state.check = crc32(state.check, input, copy, next);
          }
          have -= copy;
          next += copy;
          if (len) { break inf_leave; }
        }
        else if (state.head) {
          state.head.comment = null;
        }
        state.mode = HCRC;
        /* falls through */
      case HCRC:
        if (state.flags & 0x0200) {
          //=== NEEDBITS(16); */
          while (bits < 16) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          if (hold !== (state.check & 0xffff)) {
            strm.msg = 'header crc mismatch';
            state.mode = BAD;
            break;
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
        }
        if (state.head) {
          state.head.hcrc = ((state.flags >> 9) & 1);
          state.head.done = true;
        }
        strm.adler = state.check = 0;
        state.mode = TYPE;
        break;
      case DICTID:
        //=== NEEDBITS(32); */
        while (bits < 32) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        strm.adler = state.check = zswap32(hold);
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = DICT;
        /* falls through */
      case DICT:
        if (state.havedict === 0) {
          //--- RESTORE() ---
          strm.next_out = put;
          strm.avail_out = left;
          strm.next_in = next;
          strm.avail_in = have;
          state.hold = hold;
          state.bits = bits;
          //---
          return Z_NEED_DICT;
        }
        strm.adler = state.check = 1/*adler32(0L, Z_NULL, 0)*/;
        state.mode = TYPE;
        /* falls through */
      case TYPE:
        if (flush === Z_BLOCK || flush === Z_TREES) { break inf_leave; }
        /* falls through */
      case TYPEDO:
        if (state.last) {
          //--- BYTEBITS() ---//
          hold >>>= bits & 7;
          bits -= bits & 7;
          //---//
          state.mode = CHECK;
          break;
        }
        //=== NEEDBITS(3); */
        while (bits < 3) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        state.last = (hold & 0x01)/*BITS(1)*/;
        //--- DROPBITS(1) ---//
        hold >>>= 1;
        bits -= 1;
        //---//

        switch ((hold & 0x03)/*BITS(2)*/) {
          case 0:                             /* stored block */
            //Tracev((stderr, "inflate:     stored block%s\n",
            //        state.last ? " (last)" : ""));
            state.mode = STORED;
            break;
          case 1:                             /* fixed block */
            fixedtables(state);
            //Tracev((stderr, "inflate:     fixed codes block%s\n",
            //        state.last ? " (last)" : ""));
            state.mode = LEN_;             /* decode codes */
            if (flush === Z_TREES) {
              //--- DROPBITS(2) ---//
              hold >>>= 2;
              bits -= 2;
              //---//
              break inf_leave;
            }
            break;
          case 2:                             /* dynamic block */
            //Tracev((stderr, "inflate:     dynamic codes block%s\n",
            //        state.last ? " (last)" : ""));
            state.mode = TABLE;
            break;
          case 3:
            strm.msg = 'invalid block type';
            state.mode = BAD;
        }
        //--- DROPBITS(2) ---//
        hold >>>= 2;
        bits -= 2;
        //---//
        break;
      case STORED:
        //--- BYTEBITS() ---// /* go to byte boundary */
        hold >>>= bits & 7;
        bits -= bits & 7;
        //---//
        //=== NEEDBITS(32); */
        while (bits < 32) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        if ((hold & 0xffff) !== ((hold >>> 16) ^ 0xffff)) {
          strm.msg = 'invalid stored block lengths';
          state.mode = BAD;
          break;
        }
        state.length = hold & 0xffff;
        //Tracev((stderr, "inflate:       stored length %u\n",
        //        state.length));
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = COPY_;
        if (flush === Z_TREES) { break inf_leave; }
        /* falls through */
      case COPY_:
        state.mode = COPY;
        /* falls through */
      case COPY:
        copy = state.length;
        if (copy) {
          if (copy > have) { copy = have; }
          if (copy > left) { copy = left; }
          if (copy === 0) { break inf_leave; }
          //--- zmemcpy(put, next, copy); ---
          utils.arraySet(output, input, next, copy, put);
          //---//
          have -= copy;
          next += copy;
          left -= copy;
          put += copy;
          state.length -= copy;
          break;
        }
        //Tracev((stderr, "inflate:       stored end\n"));
        state.mode = TYPE;
        break;
      case TABLE:
        //=== NEEDBITS(14); */
        while (bits < 14) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        state.nlen = (hold & 0x1f)/*BITS(5)*/ + 257;
        //--- DROPBITS(5) ---//
        hold >>>= 5;
        bits -= 5;
        //---//
        state.ndist = (hold & 0x1f)/*BITS(5)*/ + 1;
        //--- DROPBITS(5) ---//
        hold >>>= 5;
        bits -= 5;
        //---//
        state.ncode = (hold & 0x0f)/*BITS(4)*/ + 4;
        //--- DROPBITS(4) ---//
        hold >>>= 4;
        bits -= 4;
        //---//
//#ifndef PKZIP_BUG_WORKAROUND
        if (state.nlen > 286 || state.ndist > 30) {
          strm.msg = 'too many length or distance symbols';
          state.mode = BAD;
          break;
        }
//#endif
        //Tracev((stderr, "inflate:       table sizes ok\n"));
        state.have = 0;
        state.mode = LENLENS;
        /* falls through */
      case LENLENS:
        while (state.have < state.ncode) {
          //=== NEEDBITS(3);
          while (bits < 3) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.lens[order[state.have++]] = (hold & 0x07);//BITS(3);
          //--- DROPBITS(3) ---//
          hold >>>= 3;
          bits -= 3;
          //---//
        }
        while (state.have < 19) {
          state.lens[order[state.have++]] = 0;
        }
        // We have separate tables & no pointers. 2 commented lines below not needed.
        //state.next = state.codes;
        //state.lencode = state.next;
        // Switch to use dynamic table
        state.lencode = state.lendyn;
        state.lenbits = 7;

        opts = { bits: state.lenbits };
        ret = inflate_table(CODES, state.lens, 0, 19, state.lencode, 0, state.work, opts);
        state.lenbits = opts.bits;

        if (ret) {
          strm.msg = 'invalid code lengths set';
          state.mode = BAD;
          break;
        }
        //Tracev((stderr, "inflate:       code lengths ok\n"));
        state.have = 0;
        state.mode = CODELENS;
        /* falls through */
      case CODELENS:
        while (state.have < state.nlen + state.ndist) {
          for (;;) {
            here = state.lencode[hold & ((1 << state.lenbits) - 1)];/*BITS(state.lenbits)*/
            here_bits = here >>> 24;
            here_op = (here >>> 16) & 0xff;
            here_val = here & 0xffff;

            if ((here_bits) <= bits) { break; }
            //--- PULLBYTE() ---//
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
            //---//
          }
          if (here_val < 16) {
            //--- DROPBITS(here.bits) ---//
            hold >>>= here_bits;
            bits -= here_bits;
            //---//
            state.lens[state.have++] = here_val;
          }
          else {
            if (here_val === 16) {
              //=== NEEDBITS(here.bits + 2);
              n = here_bits + 2;
              while (bits < n) {
                if (have === 0) { break inf_leave; }
                have--;
                hold += input[next++] << bits;
                bits += 8;
              }
              //===//
              //--- DROPBITS(here.bits) ---//
              hold >>>= here_bits;
              bits -= here_bits;
              //---//
              if (state.have === 0) {
                strm.msg = 'invalid bit length repeat';
                state.mode = BAD;
                break;
              }
              len = state.lens[state.have - 1];
              copy = 3 + (hold & 0x03);//BITS(2);
              //--- DROPBITS(2) ---//
              hold >>>= 2;
              bits -= 2;
              //---//
            }
            else if (here_val === 17) {
              //=== NEEDBITS(here.bits + 3);
              n = here_bits + 3;
              while (bits < n) {
                if (have === 0) { break inf_leave; }
                have--;
                hold += input[next++] << bits;
                bits += 8;
              }
              //===//
              //--- DROPBITS(here.bits) ---//
              hold >>>= here_bits;
              bits -= here_bits;
              //---//
              len = 0;
              copy = 3 + (hold & 0x07);//BITS(3);
              //--- DROPBITS(3) ---//
              hold >>>= 3;
              bits -= 3;
              //---//
            }
            else {
              //=== NEEDBITS(here.bits + 7);
              n = here_bits + 7;
              while (bits < n) {
                if (have === 0) { break inf_leave; }
                have--;
                hold += input[next++] << bits;
                bits += 8;
              }
              //===//
              //--- DROPBITS(here.bits) ---//
              hold >>>= here_bits;
              bits -= here_bits;
              //---//
              len = 0;
              copy = 11 + (hold & 0x7f);//BITS(7);
              //--- DROPBITS(7) ---//
              hold >>>= 7;
              bits -= 7;
              //---//
            }
            if (state.have + copy > state.nlen + state.ndist) {
              strm.msg = 'invalid bit length repeat';
              state.mode = BAD;
              break;
            }
            while (copy--) {
              state.lens[state.have++] = len;
            }
          }
        }

        /* handle error breaks in while */
        if (state.mode === BAD) { break; }

        /* check for end-of-block code (better have one) */
        if (state.lens[256] === 0) {
          strm.msg = 'invalid code -- missing end-of-block';
          state.mode = BAD;
          break;
        }

        /* build code tables -- note: do not change the lenbits or distbits
           values here (9 and 6) without reading the comments in inftrees.h
           concerning the ENOUGH constants, which depend on those values */
        state.lenbits = 9;

        opts = { bits: state.lenbits };
        ret = inflate_table(LENS, state.lens, 0, state.nlen, state.lencode, 0, state.work, opts);
        // We have separate tables & no pointers. 2 commented lines below not needed.
        // state.next_index = opts.table_index;
        state.lenbits = opts.bits;
        // state.lencode = state.next;

        if (ret) {
          strm.msg = 'invalid literal/lengths set';
          state.mode = BAD;
          break;
        }

        state.distbits = 6;
        //state.distcode.copy(state.codes);
        // Switch to use dynamic table
        state.distcode = state.distdyn;
        opts = { bits: state.distbits };
        ret = inflate_table(DISTS, state.lens, state.nlen, state.ndist, state.distcode, 0, state.work, opts);
        // We have separate tables & no pointers. 2 commented lines below not needed.
        // state.next_index = opts.table_index;
        state.distbits = opts.bits;
        // state.distcode = state.next;

        if (ret) {
          strm.msg = 'invalid distances set';
          state.mode = BAD;
          break;
        }
        //Tracev((stderr, 'inflate:       codes ok\n'));
        state.mode = LEN_;
        if (flush === Z_TREES) { break inf_leave; }
        /* falls through */
      case LEN_:
        state.mode = LEN;
        /* falls through */
      case LEN:
        if (have >= 6 && left >= 258) {
          //--- RESTORE() ---
          strm.next_out = put;
          strm.avail_out = left;
          strm.next_in = next;
          strm.avail_in = have;
          state.hold = hold;
          state.bits = bits;
          //---
          inflate_fast(strm, _out);
          //--- LOAD() ---
          put = strm.next_out;
          output = strm.output;
          left = strm.avail_out;
          next = strm.next_in;
          input = strm.input;
          have = strm.avail_in;
          hold = state.hold;
          bits = state.bits;
          //---

          if (state.mode === TYPE) {
            state.back = -1;
          }
          break;
        }
        state.back = 0;
        for (;;) {
          here = state.lencode[hold & ((1 << state.lenbits) - 1)];  /*BITS(state.lenbits)*/
          here_bits = here >>> 24;
          here_op = (here >>> 16) & 0xff;
          here_val = here & 0xffff;

          if (here_bits <= bits) { break; }
          //--- PULLBYTE() ---//
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
          //---//
        }
        if (here_op && (here_op & 0xf0) === 0) {
          last_bits = here_bits;
          last_op = here_op;
          last_val = here_val;
          for (;;) {
            here = state.lencode[last_val +
                    ((hold & ((1 << (last_bits + last_op)) - 1))/*BITS(last.bits + last.op)*/ >> last_bits)];
            here_bits = here >>> 24;
            here_op = (here >>> 16) & 0xff;
            here_val = here & 0xffff;

            if ((last_bits + here_bits) <= bits) { break; }
            //--- PULLBYTE() ---//
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
            //---//
          }
          //--- DROPBITS(last.bits) ---//
          hold >>>= last_bits;
          bits -= last_bits;
          //---//
          state.back += last_bits;
        }
        //--- DROPBITS(here.bits) ---//
        hold >>>= here_bits;
        bits -= here_bits;
        //---//
        state.back += here_bits;
        state.length = here_val;
        if (here_op === 0) {
          //Tracevv((stderr, here.val >= 0x20 && here.val < 0x7f ?
          //        "inflate:         literal '%c'\n" :
          //        "inflate:         literal 0x%02x\n", here.val));
          state.mode = LIT;
          break;
        }
        if (here_op & 32) {
          //Tracevv((stderr, "inflate:         end of block\n"));
          state.back = -1;
          state.mode = TYPE;
          break;
        }
        if (here_op & 64) {
          strm.msg = 'invalid literal/length code';
          state.mode = BAD;
          break;
        }
        state.extra = here_op & 15;
        state.mode = LENEXT;
        /* falls through */
      case LENEXT:
        if (state.extra) {
          //=== NEEDBITS(state.extra);
          n = state.extra;
          while (bits < n) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.length += hold & ((1 << state.extra) - 1)/*BITS(state.extra)*/;
          //--- DROPBITS(state.extra) ---//
          hold >>>= state.extra;
          bits -= state.extra;
          //---//
          state.back += state.extra;
        }
        //Tracevv((stderr, "inflate:         length %u\n", state.length));
        state.was = state.length;
        state.mode = DIST;
        /* falls through */
      case DIST:
        for (;;) {
          here = state.distcode[hold & ((1 << state.distbits) - 1)];/*BITS(state.distbits)*/
          here_bits = here >>> 24;
          here_op = (here >>> 16) & 0xff;
          here_val = here & 0xffff;

          if ((here_bits) <= bits) { break; }
          //--- PULLBYTE() ---//
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
          //---//
        }
        if ((here_op & 0xf0) === 0) {
          last_bits = here_bits;
          last_op = here_op;
          last_val = here_val;
          for (;;) {
            here = state.distcode[last_val +
                    ((hold & ((1 << (last_bits + last_op)) - 1))/*BITS(last.bits + last.op)*/ >> last_bits)];
            here_bits = here >>> 24;
            here_op = (here >>> 16) & 0xff;
            here_val = here & 0xffff;

            if ((last_bits + here_bits) <= bits) { break; }
            //--- PULLBYTE() ---//
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
            //---//
          }
          //--- DROPBITS(last.bits) ---//
          hold >>>= last_bits;
          bits -= last_bits;
          //---//
          state.back += last_bits;
        }
        //--- DROPBITS(here.bits) ---//
        hold >>>= here_bits;
        bits -= here_bits;
        //---//
        state.back += here_bits;
        if (here_op & 64) {
          strm.msg = 'invalid distance code';
          state.mode = BAD;
          break;
        }
        state.offset = here_val;
        state.extra = (here_op) & 15;
        state.mode = DISTEXT;
        /* falls through */
      case DISTEXT:
        if (state.extra) {
          //=== NEEDBITS(state.extra);
          n = state.extra;
          while (bits < n) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.offset += hold & ((1 << state.extra) - 1)/*BITS(state.extra)*/;
          //--- DROPBITS(state.extra) ---//
          hold >>>= state.extra;
          bits -= state.extra;
          //---//
          state.back += state.extra;
        }
//#ifdef INFLATE_STRICT
        if (state.offset > state.dmax) {
          strm.msg = 'invalid distance too far back';
          state.mode = BAD;
          break;
        }
//#endif
        //Tracevv((stderr, "inflate:         distance %u\n", state.offset));
        state.mode = MATCH;
        /* falls through */
      case MATCH:
        if (left === 0) { break inf_leave; }
        copy = _out - left;
        if (state.offset > copy) {         /* copy from window */
          copy = state.offset - copy;
          if (copy > state.whave) {
            if (state.sane) {
              strm.msg = 'invalid distance too far back';
              state.mode = BAD;
              break;
            }
// (!) This block is disabled in zlib defaults,
// don't enable it for binary compatibility
//#ifdef INFLATE_ALLOW_INVALID_DISTANCE_TOOFAR_ARRR
//          Trace((stderr, "inflate.c too far\n"));
//          copy -= state.whave;
//          if (copy > state.length) { copy = state.length; }
//          if (copy > left) { copy = left; }
//          left -= copy;
//          state.length -= copy;
//          do {
//            output[put++] = 0;
//          } while (--copy);
//          if (state.length === 0) { state.mode = LEN; }
//          break;
//#endif
          }
          if (copy > state.wnext) {
            copy -= state.wnext;
            from = state.wsize - copy;
          }
          else {
            from = state.wnext - copy;
          }
          if (copy > state.length) { copy = state.length; }
          from_source = state.window;
        }
        else {                              /* copy from output */
          from_source = output;
          from = put - state.offset;
          copy = state.length;
        }
        if (copy > left) { copy = left; }
        left -= copy;
        state.length -= copy;
        do {
          output[put++] = from_source[from++];
        } while (--copy);
        if (state.length === 0) { state.mode = LEN; }
        break;
      case LIT:
        if (left === 0) { break inf_leave; }
        output[put++] = state.length;
        left--;
        state.mode = LEN;
        break;
      case CHECK:
        if (state.wrap) {
          //=== NEEDBITS(32);
          while (bits < 32) {
            if (have === 0) { break inf_leave; }
            have--;
            // Use '|' instead of '+' to make sure that result is signed
            hold |= input[next++] << bits;
            bits += 8;
          }
          //===//
          _out -= left;
          strm.total_out += _out;
          state.total += _out;
          if (_out) {
            strm.adler = state.check =
                /*UPDATE(state.check, put - _out, _out);*/
                (state.flags ? crc32(state.check, output, _out, put - _out) : adler32(state.check, output, _out, put - _out));

          }
          _out = left;
          // NB: crc32 stored as signed 32-bit int, zswap32 returns signed too
          if ((state.flags ? hold : zswap32(hold)) !== state.check) {
            strm.msg = 'incorrect data check';
            state.mode = BAD;
            break;
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          //Tracev((stderr, "inflate:   check matches trailer\n"));
        }
        state.mode = LENGTH;
        /* falls through */
      case LENGTH:
        if (state.wrap && state.flags) {
          //=== NEEDBITS(32);
          while (bits < 32) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          if (hold !== (state.total & 0xffffffff)) {
            strm.msg = 'incorrect length check';
            state.mode = BAD;
            break;
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          //Tracev((stderr, "inflate:   length matches trailer\n"));
        }
        state.mode = DONE;
        /* falls through */
      case DONE:
        ret = Z_STREAM_END;
        break inf_leave;
      case BAD:
        ret = Z_DATA_ERROR;
        break inf_leave;
      case MEM:
        return Z_MEM_ERROR;
      case SYNC:
        /* falls through */
      default:
        return Z_STREAM_ERROR;
    }
  }

  // inf_leave <- here is real place for "goto inf_leave", emulated via "break inf_leave"

  /*
     Return from inflate(), updating the total counts and the check value.
     If there was no progress during the inflate() call, return a buffer
     error.  Call updatewindow() to create and/or update the window state.
     Note: a memory error from inflate() is non-recoverable.
   */

  //--- RESTORE() ---
  strm.next_out = put;
  strm.avail_out = left;
  strm.next_in = next;
  strm.avail_in = have;
  state.hold = hold;
  state.bits = bits;
  //---

  if (state.wsize || (_out !== strm.avail_out && state.mode < BAD &&
                      (state.mode < CHECK || flush !== Z_FINISH))) {
    if (updatewindow(strm, strm.output, strm.next_out, _out - strm.avail_out)) {
      state.mode = MEM;
      return Z_MEM_ERROR;
    }
  }
  _in -= strm.avail_in;
  _out -= strm.avail_out;
  strm.total_in += _in;
  strm.total_out += _out;
  state.total += _out;
  if (state.wrap && _out) {
    strm.adler = state.check = /*UPDATE(state.check, strm.next_out - _out, _out);*/
      (state.flags ? crc32(state.check, output, _out, strm.next_out - _out) : adler32(state.check, output, _out, strm.next_out - _out));
  }
  strm.data_type = state.bits + (state.last ? 64 : 0) +
                    (state.mode === TYPE ? 128 : 0) +
                    (state.mode === LEN_ || state.mode === COPY_ ? 256 : 0);
  if (((_in === 0 && _out === 0) || flush === Z_FINISH) && ret === Z_OK) {
    ret = Z_BUF_ERROR;
  }
  return ret;
}

function inflateEnd(strm) {

  if (!strm || !strm.state /*|| strm->zfree == (free_func)0*/) {
    return Z_STREAM_ERROR;
  }

  var state = strm.state;
  if (state.window) {
    state.window = null;
  }
  strm.state = null;
  return Z_OK;
}

function inflateGetHeader(strm, head) {
  var state;

  /* check state */
  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;
  if ((state.wrap & 2) === 0) { return Z_STREAM_ERROR; }

  /* save header structure */
  state.head = head;
  head.done = false;
  return Z_OK;
}

function inflateSetDictionary(strm, dictionary) {
  var dictLength = dictionary.length;

  var state;
  var dictid;
  var ret;

  /* check state */
  if (!strm /* == Z_NULL */ || !strm.state /* == Z_NULL */) { return Z_STREAM_ERROR; }
  state = strm.state;

  if (state.wrap !== 0 && state.mode !== DICT) {
    return Z_STREAM_ERROR;
  }

  /* check for correct dictionary identifier */
  if (state.mode === DICT) {
    dictid = 1; /* adler32(0, null, 0)*/
    /* dictid = adler32(dictid, dictionary, dictLength); */
    dictid = adler32(dictid, dictionary, dictLength, 0);
    if (dictid !== state.check) {
      return Z_DATA_ERROR;
    }
  }
  /* copy dictionary to window using updatewindow(), which will amend the
   existing dictionary if appropriate */
  ret = updatewindow(strm, dictionary, dictLength, dictLength);
  if (ret) {
    state.mode = MEM;
    return Z_MEM_ERROR;
  }
  state.havedict = 1;
  // Tracev((stderr, "inflate:   dictionary set\n"));
  return Z_OK;
}

exports.inflateReset = inflateReset;
exports.inflateReset2 = inflateReset2;
exports.inflateResetKeep = inflateResetKeep;
exports.inflateInit = inflateInit;
exports.inflateInit2 = inflateInit2;
exports.inflate = inflate;
exports.inflateEnd = inflateEnd;
exports.inflateGetHeader = inflateGetHeader;
exports.inflateSetDictionary = inflateSetDictionary;
exports.inflateInfo = 'pako inflate (from Nodeca project)';

/* Not implemented
exports.inflateCopy = inflateCopy;
exports.inflateGetDictionary = inflateGetDictionary;
exports.inflateMark = inflateMark;
exports.inflatePrime = inflatePrime;
exports.inflateSync = inflateSync;
exports.inflateSyncPoint = inflateSyncPoint;
exports.inflateUndermine = inflateUndermine;
*/


/***/ }),
/* 359 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

// See state defs from inflate.js
var BAD = 30;       /* got a data error -- remain here until reset */
var TYPE = 12;      /* i: waiting for type bits, including last-flag bit */

/*
   Decode literal, length, and distance codes and write out the resulting
   literal and match bytes until either not enough input or output is
   available, an end-of-block is encountered, or a data error is encountered.
   When large enough input and output buffers are supplied to inflate(), for
   example, a 16K input buffer and a 64K output buffer, more than 95% of the
   inflate execution time is spent in this routine.

   Entry assumptions:

        state.mode === LEN
        strm.avail_in >= 6
        strm.avail_out >= 258
        start >= strm.avail_out
        state.bits < 8

   On return, state.mode is one of:

        LEN -- ran out of enough output space or enough available input
        TYPE -- reached end of block code, inflate() to interpret next block
        BAD -- error in block data

   Notes:

    - The maximum input bits used by a length/distance pair is 15 bits for the
      length code, 5 bits for the length extra, 15 bits for the distance code,
      and 13 bits for the distance extra.  This totals 48 bits, or six bytes.
      Therefore if strm.avail_in >= 6, then there is enough input to avoid
      checking for available input while decoding.

    - The maximum bytes that a single length/distance pair can output is 258
      bytes, which is the maximum length that can be coded.  inflate_fast()
      requires strm.avail_out >= 258 for each loop to avoid checking for
      output space.
 */
module.exports = function inflate_fast(strm, start) {
  var state;
  var _in;                    /* local strm.input */
  var last;                   /* have enough input while in < last */
  var _out;                   /* local strm.output */
  var beg;                    /* inflate()'s initial strm.output */
  var end;                    /* while out < end, enough space available */
//#ifdef INFLATE_STRICT
  var dmax;                   /* maximum distance from zlib header */
//#endif
  var wsize;                  /* window size or zero if not using window */
  var whave;                  /* valid bytes in the window */
  var wnext;                  /* window write index */
  // Use `s_window` instead `window`, avoid conflict with instrumentation tools
  var s_window;               /* allocated sliding window, if wsize != 0 */
  var hold;                   /* local strm.hold */
  var bits;                   /* local strm.bits */
  var lcode;                  /* local strm.lencode */
  var dcode;                  /* local strm.distcode */
  var lmask;                  /* mask for first level of length codes */
  var dmask;                  /* mask for first level of distance codes */
  var here;                   /* retrieved table entry */
  var op;                     /* code bits, operation, extra bits, or */
                              /*  window position, window bytes to copy */
  var len;                    /* match length, unused bytes */
  var dist;                   /* match distance */
  var from;                   /* where to copy match from */
  var from_source;


  var input, output; // JS specific, because we have no pointers

  /* copy state to local variables */
  state = strm.state;
  //here = state.here;
  _in = strm.next_in;
  input = strm.input;
  last = _in + (strm.avail_in - 5);
  _out = strm.next_out;
  output = strm.output;
  beg = _out - (start - strm.avail_out);
  end = _out + (strm.avail_out - 257);
//#ifdef INFLATE_STRICT
  dmax = state.dmax;
//#endif
  wsize = state.wsize;
  whave = state.whave;
  wnext = state.wnext;
  s_window = state.window;
  hold = state.hold;
  bits = state.bits;
  lcode = state.lencode;
  dcode = state.distcode;
  lmask = (1 << state.lenbits) - 1;
  dmask = (1 << state.distbits) - 1;


  /* decode literals and length/distances until end-of-block or not enough
     input data or output space */

  top:
  do {
    if (bits < 15) {
      hold += input[_in++] << bits;
      bits += 8;
      hold += input[_in++] << bits;
      bits += 8;
    }

    here = lcode[hold & lmask];

    dolen:
    for (;;) { // Goto emulation
      op = here >>> 24/*here.bits*/;
      hold >>>= op;
      bits -= op;
      op = (here >>> 16) & 0xff/*here.op*/;
      if (op === 0) {                          /* literal */
        //Tracevv((stderr, here.val >= 0x20 && here.val < 0x7f ?
        //        "inflate:         literal '%c'\n" :
        //        "inflate:         literal 0x%02x\n", here.val));
        output[_out++] = here & 0xffff/*here.val*/;
      }
      else if (op & 16) {                     /* length base */
        len = here & 0xffff/*here.val*/;
        op &= 15;                           /* number of extra bits */
        if (op) {
          if (bits < op) {
            hold += input[_in++] << bits;
            bits += 8;
          }
          len += hold & ((1 << op) - 1);
          hold >>>= op;
          bits -= op;
        }
        //Tracevv((stderr, "inflate:         length %u\n", len));
        if (bits < 15) {
          hold += input[_in++] << bits;
          bits += 8;
          hold += input[_in++] << bits;
          bits += 8;
        }
        here = dcode[hold & dmask];

        dodist:
        for (;;) { // goto emulation
          op = here >>> 24/*here.bits*/;
          hold >>>= op;
          bits -= op;
          op = (here >>> 16) & 0xff/*here.op*/;

          if (op & 16) {                      /* distance base */
            dist = here & 0xffff/*here.val*/;
            op &= 15;                       /* number of extra bits */
            if (bits < op) {
              hold += input[_in++] << bits;
              bits += 8;
              if (bits < op) {
                hold += input[_in++] << bits;
                bits += 8;
              }
            }
            dist += hold & ((1 << op) - 1);
//#ifdef INFLATE_STRICT
            if (dist > dmax) {
              strm.msg = 'invalid distance too far back';
              state.mode = BAD;
              break top;
            }
//#endif
            hold >>>= op;
            bits -= op;
            //Tracevv((stderr, "inflate:         distance %u\n", dist));
            op = _out - beg;                /* max distance in output */
            if (dist > op) {                /* see if copy from window */
              op = dist - op;               /* distance back in window */
              if (op > whave) {
                if (state.sane) {
                  strm.msg = 'invalid distance too far back';
                  state.mode = BAD;
                  break top;
                }

// (!) This block is disabled in zlib defaults,
// don't enable it for binary compatibility
//#ifdef INFLATE_ALLOW_INVALID_DISTANCE_TOOFAR_ARRR
//                if (len <= op - whave) {
//                  do {
//                    output[_out++] = 0;
//                  } while (--len);
//                  continue top;
//                }
//                len -= op - whave;
//                do {
//                  output[_out++] = 0;
//                } while (--op > whave);
//                if (op === 0) {
//                  from = _out - dist;
//                  do {
//                    output[_out++] = output[from++];
//                  } while (--len);
//                  continue top;
//                }
//#endif
              }
              from = 0; // window index
              from_source = s_window;
              if (wnext === 0) {           /* very common case */
                from += wsize - op;
                if (op < len) {         /* some from window */
                  len -= op;
                  do {
                    output[_out++] = s_window[from++];
                  } while (--op);
                  from = _out - dist;  /* rest from output */
                  from_source = output;
                }
              }
              else if (wnext < op) {      /* wrap around window */
                from += wsize + wnext - op;
                op -= wnext;
                if (op < len) {         /* some from end of window */
                  len -= op;
                  do {
                    output[_out++] = s_window[from++];
                  } while (--op);
                  from = 0;
                  if (wnext < len) {  /* some from start of window */
                    op = wnext;
                    len -= op;
                    do {
                      output[_out++] = s_window[from++];
                    } while (--op);
                    from = _out - dist;      /* rest from output */
                    from_source = output;
                  }
                }
              }
              else {                      /* contiguous in window */
                from += wnext - op;
                if (op < len) {         /* some from window */
                  len -= op;
                  do {
                    output[_out++] = s_window[from++];
                  } while (--op);
                  from = _out - dist;  /* rest from output */
                  from_source = output;
                }
              }
              while (len > 2) {
                output[_out++] = from_source[from++];
                output[_out++] = from_source[from++];
                output[_out++] = from_source[from++];
                len -= 3;
              }
              if (len) {
                output[_out++] = from_source[from++];
                if (len > 1) {
                  output[_out++] = from_source[from++];
                }
              }
            }
            else {
              from = _out - dist;          /* copy direct from output */
              do {                        /* minimum length is three */
                output[_out++] = output[from++];
                output[_out++] = output[from++];
                output[_out++] = output[from++];
                len -= 3;
              } while (len > 2);
              if (len) {
                output[_out++] = output[from++];
                if (len > 1) {
                  output[_out++] = output[from++];
                }
              }
            }
          }
          else if ((op & 64) === 0) {          /* 2nd level distance code */
            here = dcode[(here & 0xffff)/*here.val*/ + (hold & ((1 << op) - 1))];
            continue dodist;
          }
          else {
            strm.msg = 'invalid distance code';
            state.mode = BAD;
            break top;
          }

          break; // need to emulate goto via "continue"
        }
      }
      else if ((op & 64) === 0) {              /* 2nd level length code */
        here = lcode[(here & 0xffff)/*here.val*/ + (hold & ((1 << op) - 1))];
        continue dolen;
      }
      else if (op & 32) {                     /* end-of-block */
        //Tracevv((stderr, "inflate:         end of block\n"));
        state.mode = TYPE;
        break top;
      }
      else {
        strm.msg = 'invalid literal/length code';
        state.mode = BAD;
        break top;
      }

      break; // need to emulate goto via "continue"
    }
  } while (_in < last && _out < end);

  /* return unused bytes (on entry, bits < 8, so in won't go too far back) */
  len = bits >> 3;
  _in -= len;
  bits -= len << 3;
  hold &= (1 << bits) - 1;

  /* update state and return */
  strm.next_in = _in;
  strm.next_out = _out;
  strm.avail_in = (_in < last ? 5 + (last - _in) : 5 - (_in - last));
  strm.avail_out = (_out < end ? 257 + (end - _out) : 257 - (_out - end));
  state.hold = hold;
  state.bits = bits;
  return;
};


/***/ }),
/* 360 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

var utils = __webpack_require__(126);

var MAXBITS = 15;
var ENOUGH_LENS = 852;
var ENOUGH_DISTS = 592;
//var ENOUGH = (ENOUGH_LENS+ENOUGH_DISTS);

var CODES = 0;
var LENS = 1;
var DISTS = 2;

var lbase = [ /* Length codes 257..285 base */
  3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31,
  35, 43, 51, 59, 67, 83, 99, 115, 131, 163, 195, 227, 258, 0, 0
];

var lext = [ /* Length codes 257..285 extra */
  16, 16, 16, 16, 16, 16, 16, 16, 17, 17, 17, 17, 18, 18, 18, 18,
  19, 19, 19, 19, 20, 20, 20, 20, 21, 21, 21, 21, 16, 72, 78
];

var dbase = [ /* Distance codes 0..29 base */
  1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193,
  257, 385, 513, 769, 1025, 1537, 2049, 3073, 4097, 6145,
  8193, 12289, 16385, 24577, 0, 0
];

var dext = [ /* Distance codes 0..29 extra */
  16, 16, 16, 16, 17, 17, 18, 18, 19, 19, 20, 20, 21, 21, 22, 22,
  23, 23, 24, 24, 25, 25, 26, 26, 27, 27,
  28, 28, 29, 29, 64, 64
];

module.exports = function inflate_table(type, lens, lens_index, codes, table, table_index, work, opts)
{
  var bits = opts.bits;
      //here = opts.here; /* table entry for duplication */

  var len = 0;               /* a code's length in bits */
  var sym = 0;               /* index of code symbols */
  var min = 0, max = 0;          /* minimum and maximum code lengths */
  var root = 0;              /* number of index bits for root table */
  var curr = 0;              /* number of index bits for current table */
  var drop = 0;              /* code bits to drop for sub-table */
  var left = 0;                   /* number of prefix codes available */
  var used = 0;              /* code entries in table used */
  var huff = 0;              /* Huffman code */
  var incr;              /* for incrementing code, index */
  var fill;              /* index for replicating entries */
  var low;               /* low bits for current root entry */
  var mask;              /* mask for low root bits */
  var next;             /* next available space in table */
  var base = null;     /* base value table to use */
  var base_index = 0;
//  var shoextra;    /* extra bits table to use */
  var end;                    /* use base and extra for symbol > end */
  var count = new utils.Buf16(MAXBITS + 1); //[MAXBITS+1];    /* number of codes of each length */
  var offs = new utils.Buf16(MAXBITS + 1); //[MAXBITS+1];     /* offsets in table for each length */
  var extra = null;
  var extra_index = 0;

  var here_bits, here_op, here_val;

  /*
   Process a set of code lengths to create a canonical Huffman code.  The
   code lengths are lens[0..codes-1].  Each length corresponds to the
   symbols 0..codes-1.  The Huffman code is generated by first sorting the
   symbols by length from short to long, and retaining the symbol order
   for codes with equal lengths.  Then the code starts with all zero bits
   for the first code of the shortest length, and the codes are integer
   increments for the same length, and zeros are appended as the length
   increases.  For the deflate format, these bits are stored backwards
   from their more natural integer increment ordering, and so when the
   decoding tables are built in the large loop below, the integer codes
   are incremented backwards.

   This routine assumes, but does not check, that all of the entries in
   lens[] are in the range 0..MAXBITS.  The caller must assure this.
   1..MAXBITS is interpreted as that code length.  zero means that that
   symbol does not occur in this code.

   The codes are sorted by computing a count of codes for each length,
   creating from that a table of starting indices for each length in the
   sorted table, and then entering the symbols in order in the sorted
   table.  The sorted table is work[], with that space being provided by
   the caller.

   The length counts are used for other purposes as well, i.e. finding
   the minimum and maximum length codes, determining if there are any
   codes at all, checking for a valid set of lengths, and looking ahead
   at length counts to determine sub-table sizes when building the
   decoding tables.
   */

  /* accumulate lengths for codes (assumes lens[] all in 0..MAXBITS) */
  for (len = 0; len <= MAXBITS; len++) {
    count[len] = 0;
  }
  for (sym = 0; sym < codes; sym++) {
    count[lens[lens_index + sym]]++;
  }

  /* bound code lengths, force root to be within code lengths */
  root = bits;
  for (max = MAXBITS; max >= 1; max--) {
    if (count[max] !== 0) { break; }
  }
  if (root > max) {
    root = max;
  }
  if (max === 0) {                     /* no symbols to code at all */
    //table.op[opts.table_index] = 64;  //here.op = (var char)64;    /* invalid code marker */
    //table.bits[opts.table_index] = 1;   //here.bits = (var char)1;
    //table.val[opts.table_index++] = 0;   //here.val = (var short)0;
    table[table_index++] = (1 << 24) | (64 << 16) | 0;


    //table.op[opts.table_index] = 64;
    //table.bits[opts.table_index] = 1;
    //table.val[opts.table_index++] = 0;
    table[table_index++] = (1 << 24) | (64 << 16) | 0;

    opts.bits = 1;
    return 0;     /* no symbols, but wait for decoding to report error */
  }
  for (min = 1; min < max; min++) {
    if (count[min] !== 0) { break; }
  }
  if (root < min) {
    root = min;
  }

  /* check for an over-subscribed or incomplete set of lengths */
  left = 1;
  for (len = 1; len <= MAXBITS; len++) {
    left <<= 1;
    left -= count[len];
    if (left < 0) {
      return -1;
    }        /* over-subscribed */
  }
  if (left > 0 && (type === CODES || max !== 1)) {
    return -1;                      /* incomplete set */
  }

  /* generate offsets into symbol table for each length for sorting */
  offs[1] = 0;
  for (len = 1; len < MAXBITS; len++) {
    offs[len + 1] = offs[len] + count[len];
  }

  /* sort symbols by length, by symbol order within each length */
  for (sym = 0; sym < codes; sym++) {
    if (lens[lens_index + sym] !== 0) {
      work[offs[lens[lens_index + sym]]++] = sym;
    }
  }

  /*
   Create and fill in decoding tables.  In this loop, the table being
   filled is at next and has curr index bits.  The code being used is huff
   with length len.  That code is converted to an index by dropping drop
   bits off of the bottom.  For codes where len is less than drop + curr,
   those top drop + curr - len bits are incremented through all values to
   fill the table with replicated entries.

   root is the number of index bits for the root table.  When len exceeds
   root, sub-tables are created pointed to by the root entry with an index
   of the low root bits of huff.  This is saved in low to check for when a
   new sub-table should be started.  drop is zero when the root table is
   being filled, and drop is root when sub-tables are being filled.

   When a new sub-table is needed, it is necessary to look ahead in the
   code lengths to determine what size sub-table is needed.  The length
   counts are used for this, and so count[] is decremented as codes are
   entered in the tables.

   used keeps track of how many table entries have been allocated from the
   provided *table space.  It is checked for LENS and DIST tables against
   the constants ENOUGH_LENS and ENOUGH_DISTS to guard against changes in
   the initial root table size constants.  See the comments in inftrees.h
   for more information.

   sym increments through all symbols, and the loop terminates when
   all codes of length max, i.e. all codes, have been processed.  This
   routine permits incomplete codes, so another loop after this one fills
   in the rest of the decoding tables with invalid code markers.
   */

  /* set up for code type */
  // poor man optimization - use if-else instead of switch,
  // to avoid deopts in old v8
  if (type === CODES) {
    base = extra = work;    /* dummy value--not used */
    end = 19;

  } else if (type === LENS) {
    base = lbase;
    base_index -= 257;
    extra = lext;
    extra_index -= 257;
    end = 256;

  } else {                    /* DISTS */
    base = dbase;
    extra = dext;
    end = -1;
  }

  /* initialize opts for loop */
  huff = 0;                   /* starting code */
  sym = 0;                    /* starting code symbol */
  len = min;                  /* starting code length */
  next = table_index;              /* current table to fill in */
  curr = root;                /* current table index bits */
  drop = 0;                   /* current bits to drop from code for index */
  low = -1;                   /* trigger new sub-table when len > root */
  used = 1 << root;          /* use root table entries */
  mask = used - 1;            /* mask for comparing low */

  /* check available table space */
  if ((type === LENS && used > ENOUGH_LENS) ||
    (type === DISTS && used > ENOUGH_DISTS)) {
    return 1;
  }

  /* process all codes and make table entries */
  for (;;) {
    /* create table entry */
    here_bits = len - drop;
    if (work[sym] < end) {
      here_op = 0;
      here_val = work[sym];
    }
    else if (work[sym] > end) {
      here_op = extra[extra_index + work[sym]];
      here_val = base[base_index + work[sym]];
    }
    else {
      here_op = 32 + 64;         /* end of block */
      here_val = 0;
    }

    /* replicate for those indices with low len bits equal to huff */
    incr = 1 << (len - drop);
    fill = 1 << curr;
    min = fill;                 /* save offset to next table */
    do {
      fill -= incr;
      table[next + (huff >> drop) + fill] = (here_bits << 24) | (here_op << 16) | here_val |0;
    } while (fill !== 0);

    /* backwards increment the len-bit code huff */
    incr = 1 << (len - 1);
    while (huff & incr) {
      incr >>= 1;
    }
    if (incr !== 0) {
      huff &= incr - 1;
      huff += incr;
    } else {
      huff = 0;
    }

    /* go to next symbol, update count, len */
    sym++;
    if (--count[len] === 0) {
      if (len === max) { break; }
      len = lens[lens_index + work[sym]];
    }

    /* create new sub-table if needed */
    if (len > root && (huff & mask) !== low) {
      /* if first time, transition to sub-tables */
      if (drop === 0) {
        drop = root;
      }

      /* increment past last table */
      next += min;            /* here min is 1 << curr */

      /* determine length of next table */
      curr = len - drop;
      left = 1 << curr;
      while (curr + drop < max) {
        left -= count[curr + drop];
        if (left <= 0) { break; }
        curr++;
        left <<= 1;
      }

      /* check for enough space */
      used += 1 << curr;
      if ((type === LENS && used > ENOUGH_LENS) ||
        (type === DISTS && used > ENOUGH_DISTS)) {
        return 1;
      }

      /* point entry in root table to sub-table */
      low = huff & mask;
      /*table.op[low] = curr;
      table.bits[low] = root;
      table.val[low] = next - opts.table_index;*/
      table[low] = (root << 24) | (curr << 16) | (next - table_index) |0;
    }
  }

  /* fill in remaining table entry if code is incomplete (guaranteed to have
   at most one remaining entry, since if the code is incomplete, the
   maximum code length that was allowed to get this far is one bit) */
  if (huff !== 0) {
    //table.op[next + huff] = 64;            /* invalid code marker */
    //table.bits[next + huff] = len - drop;
    //table.val[next + huff] = 0;
    table[next + huff] = ((len - drop) << 24) | (64 << 16) |0;
  }

  /* set return parameters */
  //opts.table_index += used;
  opts.bits = root;
  return 0;
};


/***/ }),
/* 361 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

module.exports = {

  /* Allowed flush values; see deflate() and inflate() below for details */
  Z_NO_FLUSH:         0,
  Z_PARTIAL_FLUSH:    1,
  Z_SYNC_FLUSH:       2,
  Z_FULL_FLUSH:       3,
  Z_FINISH:           4,
  Z_BLOCK:            5,
  Z_TREES:            6,

  /* Return codes for the compression/decompression functions. Negative values
  * are errors, positive values are used for special but normal events.
  */
  Z_OK:               0,
  Z_STREAM_END:       1,
  Z_NEED_DICT:        2,
  Z_ERRNO:           -1,
  Z_STREAM_ERROR:    -2,
  Z_DATA_ERROR:      -3,
  //Z_MEM_ERROR:     -4,
  Z_BUF_ERROR:       -5,
  //Z_VERSION_ERROR: -6,

  /* compression levels */
  Z_NO_COMPRESSION:         0,
  Z_BEST_SPEED:             1,
  Z_BEST_COMPRESSION:       9,
  Z_DEFAULT_COMPRESSION:   -1,


  Z_FILTERED:               1,
  Z_HUFFMAN_ONLY:           2,
  Z_RLE:                    3,
  Z_FIXED:                  4,
  Z_DEFAULT_STRATEGY:       0,

  /* Possible values of the data_type field (though see inflate()) */
  Z_BINARY:                 0,
  Z_TEXT:                   1,
  //Z_ASCII:                1, // = Z_TEXT (deprecated)
  Z_UNKNOWN:                2,

  /* The deflate compression method */
  Z_DEFLATED:               8
  //Z_NULL:                 null // Use -1 or null inline, depending on var type
};


/***/ }),
/* 362 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(127), __webpack_require__(363), __webpack_require__(364), __webpack_require__(87), __webpack_require__(88), __webpack_require__(167), __webpack_require__(248), __webpack_require__(365), __webpack_require__(249), __webpack_require__(366), __webpack_require__(367), __webpack_require__(368), __webpack_require__(168), __webpack_require__(369), __webpack_require__(49), __webpack_require__(12), __webpack_require__(370), __webpack_require__(371), __webpack_require__(372), __webpack_require__(373), __webpack_require__(374), __webpack_require__(375), __webpack_require__(376), __webpack_require__(377), __webpack_require__(378), __webpack_require__(379), __webpack_require__(380), __webpack_require__(381), __webpack_require__(382), __webpack_require__(383), __webpack_require__(384), __webpack_require__(385));
	}
	else {}
}(this, function (CryptoJS) {

	return CryptoJS;

}));

/***/ }),
/* 363 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Check if typed arrays are supported
	    if (typeof ArrayBuffer != 'function') {
	        return;
	    }

	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var WordArray = C_lib.WordArray;

	    // Reference original init
	    var superInit = WordArray.init;

	    // Augment WordArray.init to handle typed arrays
	    var subInit = WordArray.init = function (typedArray) {
	        // Convert buffers to uint8
	        if (typedArray instanceof ArrayBuffer) {
	            typedArray = new Uint8Array(typedArray);
	        }

	        // Convert other array views to uint8
	        if (
	            typedArray instanceof Int8Array ||
	            (typeof Uint8ClampedArray !== "undefined" && typedArray instanceof Uint8ClampedArray) ||
	            typedArray instanceof Int16Array ||
	            typedArray instanceof Uint16Array ||
	            typedArray instanceof Int32Array ||
	            typedArray instanceof Uint32Array ||
	            typedArray instanceof Float32Array ||
	            typedArray instanceof Float64Array
	        ) {
	            typedArray = new Uint8Array(typedArray.buffer, typedArray.byteOffset, typedArray.byteLength);
	        }

	        // Handle Uint8Array
	        if (typedArray instanceof Uint8Array) {
	            // Shortcut
	            var typedArrayByteLength = typedArray.byteLength;

	            // Extract bytes
	            var words = [];
	            for (var i = 0; i < typedArrayByteLength; i++) {
	                words[i >>> 2] |= typedArray[i] << (24 - (i % 4) * 8);
	            }

	            // Initialize this word array
	            superInit.call(this, words, typedArrayByteLength);
	        } else {
	            // Else call normal init
	            superInit.apply(this, arguments);
	        }
	    };

	    subInit.prototype = WordArray;
	}());


	return CryptoJS.lib.WordArray;

}));

/***/ }),
/* 364 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var WordArray = C_lib.WordArray;
	    var C_enc = C.enc;

	    /**
	     * UTF-16 BE encoding strategy.
	     */
	    var Utf16BE = C_enc.Utf16 = C_enc.Utf16BE = {
	        /**
	         * Converts a word array to a UTF-16 BE string.
	         *
	         * @param {WordArray} wordArray The word array.
	         *
	         * @return {string} The UTF-16 BE string.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var utf16String = CryptoJS.enc.Utf16.stringify(wordArray);
	         */
	        stringify: function (wordArray) {
	            // Shortcuts
	            var words = wordArray.words;
	            var sigBytes = wordArray.sigBytes;

	            // Convert
	            var utf16Chars = [];
	            for (var i = 0; i < sigBytes; i += 2) {
	                var codePoint = (words[i >>> 2] >>> (16 - (i % 4) * 8)) & 0xffff;
	                utf16Chars.push(String.fromCharCode(codePoint));
	            }

	            return utf16Chars.join('');
	        },

	        /**
	         * Converts a UTF-16 BE string to a word array.
	         *
	         * @param {string} utf16Str The UTF-16 BE string.
	         *
	         * @return {WordArray} The word array.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var wordArray = CryptoJS.enc.Utf16.parse(utf16String);
	         */
	        parse: function (utf16Str) {
	            // Shortcut
	            var utf16StrLength = utf16Str.length;

	            // Convert
	            var words = [];
	            for (var i = 0; i < utf16StrLength; i++) {
	                words[i >>> 1] |= utf16Str.charCodeAt(i) << (16 - (i % 2) * 16);
	            }

	            return WordArray.create(words, utf16StrLength * 2);
	        }
	    };

	    /**
	     * UTF-16 LE encoding strategy.
	     */
	    C_enc.Utf16LE = {
	        /**
	         * Converts a word array to a UTF-16 LE string.
	         *
	         * @param {WordArray} wordArray The word array.
	         *
	         * @return {string} The UTF-16 LE string.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var utf16Str = CryptoJS.enc.Utf16LE.stringify(wordArray);
	         */
	        stringify: function (wordArray) {
	            // Shortcuts
	            var words = wordArray.words;
	            var sigBytes = wordArray.sigBytes;

	            // Convert
	            var utf16Chars = [];
	            for (var i = 0; i < sigBytes; i += 2) {
	                var codePoint = swapEndian((words[i >>> 2] >>> (16 - (i % 4) * 8)) & 0xffff);
	                utf16Chars.push(String.fromCharCode(codePoint));
	            }

	            return utf16Chars.join('');
	        },

	        /**
	         * Converts a UTF-16 LE string to a word array.
	         *
	         * @param {string} utf16Str The UTF-16 LE string.
	         *
	         * @return {WordArray} The word array.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var wordArray = CryptoJS.enc.Utf16LE.parse(utf16Str);
	         */
	        parse: function (utf16Str) {
	            // Shortcut
	            var utf16StrLength = utf16Str.length;

	            // Convert
	            var words = [];
	            for (var i = 0; i < utf16StrLength; i++) {
	                words[i >>> 1] |= swapEndian(utf16Str.charCodeAt(i) << (16 - (i % 2) * 16));
	            }

	            return WordArray.create(words, utf16StrLength * 2);
	        }
	    };

	    function swapEndian(word) {
	        return ((word << 8) & 0xff00ff00) | ((word >>> 8) & 0x00ff00ff);
	    }
	}());


	return CryptoJS.enc.Utf16;

}));

/***/ }),
/* 365 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(248));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var WordArray = C_lib.WordArray;
	    var C_algo = C.algo;
	    var SHA256 = C_algo.SHA256;

	    /**
	     * SHA-224 hash algorithm.
	     */
	    var SHA224 = C_algo.SHA224 = SHA256.extend({
	        _doReset: function () {
	            this._hash = new WordArray.init([
	                0xc1059ed8, 0x367cd507, 0x3070dd17, 0xf70e5939,
	                0xffc00b31, 0x68581511, 0x64f98fa7, 0xbefa4fa4
	            ]);
	        },

	        _doFinalize: function () {
	            var hash = SHA256._doFinalize.call(this);

	            hash.sigBytes -= 4;

	            return hash;
	        }
	    });

	    /**
	     * Shortcut function to the hasher's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     *
	     * @return {WordArray} The hash.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hash = CryptoJS.SHA224('message');
	     *     var hash = CryptoJS.SHA224(wordArray);
	     */
	    C.SHA224 = SHA256._createHelper(SHA224);

	    /**
	     * Shortcut function to the HMAC's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     * @param {WordArray|string} key The secret key.
	     *
	     * @return {WordArray} The HMAC.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hmac = CryptoJS.HmacSHA224(message, key);
	     */
	    C.HmacSHA224 = SHA256._createHmacHelper(SHA224);
	}());


	return CryptoJS.SHA224;

}));

/***/ }),
/* 366 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(127), __webpack_require__(249));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_x64 = C.x64;
	    var X64Word = C_x64.Word;
	    var X64WordArray = C_x64.WordArray;
	    var C_algo = C.algo;
	    var SHA512 = C_algo.SHA512;

	    /**
	     * SHA-384 hash algorithm.
	     */
	    var SHA384 = C_algo.SHA384 = SHA512.extend({
	        _doReset: function () {
	            this._hash = new X64WordArray.init([
	                new X64Word.init(0xcbbb9d5d, 0xc1059ed8), new X64Word.init(0x629a292a, 0x367cd507),
	                new X64Word.init(0x9159015a, 0x3070dd17), new X64Word.init(0x152fecd8, 0xf70e5939),
	                new X64Word.init(0x67332667, 0xffc00b31), new X64Word.init(0x8eb44a87, 0x68581511),
	                new X64Word.init(0xdb0c2e0d, 0x64f98fa7), new X64Word.init(0x47b5481d, 0xbefa4fa4)
	            ]);
	        },

	        _doFinalize: function () {
	            var hash = SHA512._doFinalize.call(this);

	            hash.sigBytes -= 16;

	            return hash;
	        }
	    });

	    /**
	     * Shortcut function to the hasher's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     *
	     * @return {WordArray} The hash.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hash = CryptoJS.SHA384('message');
	     *     var hash = CryptoJS.SHA384(wordArray);
	     */
	    C.SHA384 = SHA512._createHelper(SHA384);

	    /**
	     * Shortcut function to the HMAC's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     * @param {WordArray|string} key The secret key.
	     *
	     * @return {WordArray} The HMAC.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hmac = CryptoJS.HmacSHA384(message, key);
	     */
	    C.HmacSHA384 = SHA512._createHmacHelper(SHA384);
	}());


	return CryptoJS.SHA384;

}));

/***/ }),
/* 367 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(127));
	}
	else {}
}(this, function (CryptoJS) {

	(function (Math) {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var WordArray = C_lib.WordArray;
	    var Hasher = C_lib.Hasher;
	    var C_x64 = C.x64;
	    var X64Word = C_x64.Word;
	    var C_algo = C.algo;

	    // Constants tables
	    var RHO_OFFSETS = [];
	    var PI_INDEXES  = [];
	    var ROUND_CONSTANTS = [];

	    // Compute Constants
	    (function () {
	        // Compute rho offset constants
	        var x = 1, y = 0;
	        for (var t = 0; t < 24; t++) {
	            RHO_OFFSETS[x + 5 * y] = ((t + 1) * (t + 2) / 2) % 64;

	            var newX = y % 5;
	            var newY = (2 * x + 3 * y) % 5;
	            x = newX;
	            y = newY;
	        }

	        // Compute pi index constants
	        for (var x = 0; x < 5; x++) {
	            for (var y = 0; y < 5; y++) {
	                PI_INDEXES[x + 5 * y] = y + ((2 * x + 3 * y) % 5) * 5;
	            }
	        }

	        // Compute round constants
	        var LFSR = 0x01;
	        for (var i = 0; i < 24; i++) {
	            var roundConstantMsw = 0;
	            var roundConstantLsw = 0;

	            for (var j = 0; j < 7; j++) {
	                if (LFSR & 0x01) {
	                    var bitPosition = (1 << j) - 1;
	                    if (bitPosition < 32) {
	                        roundConstantLsw ^= 1 << bitPosition;
	                    } else /* if (bitPosition >= 32) */ {
	                        roundConstantMsw ^= 1 << (bitPosition - 32);
	                    }
	                }

	                // Compute next LFSR
	                if (LFSR & 0x80) {
	                    // Primitive polynomial over GF(2): x^8 + x^6 + x^5 + x^4 + 1
	                    LFSR = (LFSR << 1) ^ 0x71;
	                } else {
	                    LFSR <<= 1;
	                }
	            }

	            ROUND_CONSTANTS[i] = X64Word.create(roundConstantMsw, roundConstantLsw);
	        }
	    }());

	    // Reusable objects for temporary values
	    var T = [];
	    (function () {
	        for (var i = 0; i < 25; i++) {
	            T[i] = X64Word.create();
	        }
	    }());

	    /**
	     * SHA-3 hash algorithm.
	     */
	    var SHA3 = C_algo.SHA3 = Hasher.extend({
	        /**
	         * Configuration options.
	         *
	         * @property {number} outputLength
	         *   The desired number of bits in the output hash.
	         *   Only values permitted are: 224, 256, 384, 512.
	         *   Default: 512
	         */
	        cfg: Hasher.cfg.extend({
	            outputLength: 512
	        }),

	        _doReset: function () {
	            var state = this._state = []
	            for (var i = 0; i < 25; i++) {
	                state[i] = new X64Word.init();
	            }

	            this.blockSize = (1600 - 2 * this.cfg.outputLength) / 32;
	        },

	        _doProcessBlock: function (M, offset) {
	            // Shortcuts
	            var state = this._state;
	            var nBlockSizeLanes = this.blockSize / 2;

	            // Absorb
	            for (var i = 0; i < nBlockSizeLanes; i++) {
	                // Shortcuts
	                var M2i  = M[offset + 2 * i];
	                var M2i1 = M[offset + 2 * i + 1];

	                // Swap endian
	                M2i = (
	                    (((M2i << 8)  | (M2i >>> 24)) & 0x00ff00ff) |
	                    (((M2i << 24) | (M2i >>> 8))  & 0xff00ff00)
	                );
	                M2i1 = (
	                    (((M2i1 << 8)  | (M2i1 >>> 24)) & 0x00ff00ff) |
	                    (((M2i1 << 24) | (M2i1 >>> 8))  & 0xff00ff00)
	                );

	                // Absorb message into state
	                var lane = state[i];
	                lane.high ^= M2i1;
	                lane.low  ^= M2i;
	            }

	            // Rounds
	            for (var round = 0; round < 24; round++) {
	                // Theta
	                for (var x = 0; x < 5; x++) {
	                    // Mix column lanes
	                    var tMsw = 0, tLsw = 0;
	                    for (var y = 0; y < 5; y++) {
	                        var lane = state[x + 5 * y];
	                        tMsw ^= lane.high;
	                        tLsw ^= lane.low;
	                    }

	                    // Temporary values
	                    var Tx = T[x];
	                    Tx.high = tMsw;
	                    Tx.low  = tLsw;
	                }
	                for (var x = 0; x < 5; x++) {
	                    // Shortcuts
	                    var Tx4 = T[(x + 4) % 5];
	                    var Tx1 = T[(x + 1) % 5];
	                    var Tx1Msw = Tx1.high;
	                    var Tx1Lsw = Tx1.low;

	                    // Mix surrounding columns
	                    var tMsw = Tx4.high ^ ((Tx1Msw << 1) | (Tx1Lsw >>> 31));
	                    var tLsw = Tx4.low  ^ ((Tx1Lsw << 1) | (Tx1Msw >>> 31));
	                    for (var y = 0; y < 5; y++) {
	                        var lane = state[x + 5 * y];
	                        lane.high ^= tMsw;
	                        lane.low  ^= tLsw;
	                    }
	                }

	                // Rho Pi
	                for (var laneIndex = 1; laneIndex < 25; laneIndex++) {
	                    // Shortcuts
	                    var lane = state[laneIndex];
	                    var laneMsw = lane.high;
	                    var laneLsw = lane.low;
	                    var rhoOffset = RHO_OFFSETS[laneIndex];

	                    // Rotate lanes
	                    if (rhoOffset < 32) {
	                        var tMsw = (laneMsw << rhoOffset) | (laneLsw >>> (32 - rhoOffset));
	                        var tLsw = (laneLsw << rhoOffset) | (laneMsw >>> (32 - rhoOffset));
	                    } else /* if (rhoOffset >= 32) */ {
	                        var tMsw = (laneLsw << (rhoOffset - 32)) | (laneMsw >>> (64 - rhoOffset));
	                        var tLsw = (laneMsw << (rhoOffset - 32)) | (laneLsw >>> (64 - rhoOffset));
	                    }

	                    // Transpose lanes
	                    var TPiLane = T[PI_INDEXES[laneIndex]];
	                    TPiLane.high = tMsw;
	                    TPiLane.low  = tLsw;
	                }

	                // Rho pi at x = y = 0
	                var T0 = T[0];
	                var state0 = state[0];
	                T0.high = state0.high;
	                T0.low  = state0.low;

	                // Chi
	                for (var x = 0; x < 5; x++) {
	                    for (var y = 0; y < 5; y++) {
	                        // Shortcuts
	                        var laneIndex = x + 5 * y;
	                        var lane = state[laneIndex];
	                        var TLane = T[laneIndex];
	                        var Tx1Lane = T[((x + 1) % 5) + 5 * y];
	                        var Tx2Lane = T[((x + 2) % 5) + 5 * y];

	                        // Mix rows
	                        lane.high = TLane.high ^ (~Tx1Lane.high & Tx2Lane.high);
	                        lane.low  = TLane.low  ^ (~Tx1Lane.low  & Tx2Lane.low);
	                    }
	                }

	                // Iota
	                var lane = state[0];
	                var roundConstant = ROUND_CONSTANTS[round];
	                lane.high ^= roundConstant.high;
	                lane.low  ^= roundConstant.low;;
	            }
	        },

	        _doFinalize: function () {
	            // Shortcuts
	            var data = this._data;
	            var dataWords = data.words;
	            var nBitsTotal = this._nDataBytes * 8;
	            var nBitsLeft = data.sigBytes * 8;
	            var blockSizeBits = this.blockSize * 32;

	            // Add padding
	            dataWords[nBitsLeft >>> 5] |= 0x1 << (24 - nBitsLeft % 32);
	            dataWords[((Math.ceil((nBitsLeft + 1) / blockSizeBits) * blockSizeBits) >>> 5) - 1] |= 0x80;
	            data.sigBytes = dataWords.length * 4;

	            // Hash final blocks
	            this._process();

	            // Shortcuts
	            var state = this._state;
	            var outputLengthBytes = this.cfg.outputLength / 8;
	            var outputLengthLanes = outputLengthBytes / 8;

	            // Squeeze
	            var hashWords = [];
	            for (var i = 0; i < outputLengthLanes; i++) {
	                // Shortcuts
	                var lane = state[i];
	                var laneMsw = lane.high;
	                var laneLsw = lane.low;

	                // Swap endian
	                laneMsw = (
	                    (((laneMsw << 8)  | (laneMsw >>> 24)) & 0x00ff00ff) |
	                    (((laneMsw << 24) | (laneMsw >>> 8))  & 0xff00ff00)
	                );
	                laneLsw = (
	                    (((laneLsw << 8)  | (laneLsw >>> 24)) & 0x00ff00ff) |
	                    (((laneLsw << 24) | (laneLsw >>> 8))  & 0xff00ff00)
	                );

	                // Squeeze state to retrieve hash
	                hashWords.push(laneLsw);
	                hashWords.push(laneMsw);
	            }

	            // Return final computed hash
	            return new WordArray.init(hashWords, outputLengthBytes);
	        },

	        clone: function () {
	            var clone = Hasher.clone.call(this);

	            var state = clone._state = this._state.slice(0);
	            for (var i = 0; i < 25; i++) {
	                state[i] = state[i].clone();
	            }

	            return clone;
	        }
	    });

	    /**
	     * Shortcut function to the hasher's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     *
	     * @return {WordArray} The hash.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hash = CryptoJS.SHA3('message');
	     *     var hash = CryptoJS.SHA3(wordArray);
	     */
	    C.SHA3 = Hasher._createHelper(SHA3);

	    /**
	     * Shortcut function to the HMAC's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     * @param {WordArray|string} key The secret key.
	     *
	     * @return {WordArray} The HMAC.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hmac = CryptoJS.HmacSHA3(message, key);
	     */
	    C.HmacSHA3 = Hasher._createHmacHelper(SHA3);
	}(Math));


	return CryptoJS.SHA3;

}));

/***/ }),
/* 368 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2));
	}
	else {}
}(this, function (CryptoJS) {

	/** @preserve
	(c) 2012 by Cédric Mesnil. All rights reserved.

	Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:

	    - Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
	    - Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.

	THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
	*/

	(function (Math) {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var WordArray = C_lib.WordArray;
	    var Hasher = C_lib.Hasher;
	    var C_algo = C.algo;

	    // Constants table
	    var _zl = WordArray.create([
	        0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15,
	        7,  4, 13,  1, 10,  6, 15,  3, 12,  0,  9,  5,  2, 14, 11,  8,
	        3, 10, 14,  4,  9, 15,  8,  1,  2,  7,  0,  6, 13, 11,  5, 12,
	        1,  9, 11, 10,  0,  8, 12,  4, 13,  3,  7, 15, 14,  5,  6,  2,
	        4,  0,  5,  9,  7, 12,  2, 10, 14,  1,  3,  8, 11,  6, 15, 13]);
	    var _zr = WordArray.create([
	        5, 14,  7,  0,  9,  2, 11,  4, 13,  6, 15,  8,  1, 10,  3, 12,
	        6, 11,  3,  7,  0, 13,  5, 10, 14, 15,  8, 12,  4,  9,  1,  2,
	        15,  5,  1,  3,  7, 14,  6,  9, 11,  8, 12,  2, 10,  0,  4, 13,
	        8,  6,  4,  1,  3, 11, 15,  0,  5, 12,  2, 13,  9,  7, 10, 14,
	        12, 15, 10,  4,  1,  5,  8,  7,  6,  2, 13, 14,  0,  3,  9, 11]);
	    var _sl = WordArray.create([
	         11, 14, 15, 12,  5,  8,  7,  9, 11, 13, 14, 15,  6,  7,  9,  8,
	        7, 6,   8, 13, 11,  9,  7, 15,  7, 12, 15,  9, 11,  7, 13, 12,
	        11, 13,  6,  7, 14,  9, 13, 15, 14,  8, 13,  6,  5, 12,  7,  5,
	          11, 12, 14, 15, 14, 15,  9,  8,  9, 14,  5,  6,  8,  6,  5, 12,
	        9, 15,  5, 11,  6,  8, 13, 12,  5, 12, 13, 14, 11,  8,  5,  6 ]);
	    var _sr = WordArray.create([
	        8,  9,  9, 11, 13, 15, 15,  5,  7,  7,  8, 11, 14, 14, 12,  6,
	        9, 13, 15,  7, 12,  8,  9, 11,  7,  7, 12,  7,  6, 15, 13, 11,
	        9,  7, 15, 11,  8,  6,  6, 14, 12, 13,  5, 14, 13, 13,  7,  5,
	        15,  5,  8, 11, 14, 14,  6, 14,  6,  9, 12,  9, 12,  5, 15,  8,
	        8,  5, 12,  9, 12,  5, 14,  6,  8, 13,  6,  5, 15, 13, 11, 11 ]);

	    var _hl =  WordArray.create([ 0x00000000, 0x5A827999, 0x6ED9EBA1, 0x8F1BBCDC, 0xA953FD4E]);
	    var _hr =  WordArray.create([ 0x50A28BE6, 0x5C4DD124, 0x6D703EF3, 0x7A6D76E9, 0x00000000]);

	    /**
	     * RIPEMD160 hash algorithm.
	     */
	    var RIPEMD160 = C_algo.RIPEMD160 = Hasher.extend({
	        _doReset: function () {
	            this._hash  = WordArray.create([0x67452301, 0xEFCDAB89, 0x98BADCFE, 0x10325476, 0xC3D2E1F0]);
	        },

	        _doProcessBlock: function (M, offset) {

	            // Swap endian
	            for (var i = 0; i < 16; i++) {
	                // Shortcuts
	                var offset_i = offset + i;
	                var M_offset_i = M[offset_i];

	                // Swap
	                M[offset_i] = (
	                    (((M_offset_i << 8)  | (M_offset_i >>> 24)) & 0x00ff00ff) |
	                    (((M_offset_i << 24) | (M_offset_i >>> 8))  & 0xff00ff00)
	                );
	            }
	            // Shortcut
	            var H  = this._hash.words;
	            var hl = _hl.words;
	            var hr = _hr.words;
	            var zl = _zl.words;
	            var zr = _zr.words;
	            var sl = _sl.words;
	            var sr = _sr.words;

	            // Working variables
	            var al, bl, cl, dl, el;
	            var ar, br, cr, dr, er;

	            ar = al = H[0];
	            br = bl = H[1];
	            cr = cl = H[2];
	            dr = dl = H[3];
	            er = el = H[4];
	            // Computation
	            var t;
	            for (var i = 0; i < 80; i += 1) {
	                t = (al +  M[offset+zl[i]])|0;
	                if (i<16){
		            t +=  f1(bl,cl,dl) + hl[0];
	                } else if (i<32) {
		            t +=  f2(bl,cl,dl) + hl[1];
	                } else if (i<48) {
		            t +=  f3(bl,cl,dl) + hl[2];
	                } else if (i<64) {
		            t +=  f4(bl,cl,dl) + hl[3];
	                } else {// if (i<80) {
		            t +=  f5(bl,cl,dl) + hl[4];
	                }
	                t = t|0;
	                t =  rotl(t,sl[i]);
	                t = (t+el)|0;
	                al = el;
	                el = dl;
	                dl = rotl(cl, 10);
	                cl = bl;
	                bl = t;

	                t = (ar + M[offset+zr[i]])|0;
	                if (i<16){
		            t +=  f5(br,cr,dr) + hr[0];
	                } else if (i<32) {
		            t +=  f4(br,cr,dr) + hr[1];
	                } else if (i<48) {
		            t +=  f3(br,cr,dr) + hr[2];
	                } else if (i<64) {
		            t +=  f2(br,cr,dr) + hr[3];
	                } else {// if (i<80) {
		            t +=  f1(br,cr,dr) + hr[4];
	                }
	                t = t|0;
	                t =  rotl(t,sr[i]) ;
	                t = (t+er)|0;
	                ar = er;
	                er = dr;
	                dr = rotl(cr, 10);
	                cr = br;
	                br = t;
	            }
	            // Intermediate hash value
	            t    = (H[1] + cl + dr)|0;
	            H[1] = (H[2] + dl + er)|0;
	            H[2] = (H[3] + el + ar)|0;
	            H[3] = (H[4] + al + br)|0;
	            H[4] = (H[0] + bl + cr)|0;
	            H[0] =  t;
	        },

	        _doFinalize: function () {
	            // Shortcuts
	            var data = this._data;
	            var dataWords = data.words;

	            var nBitsTotal = this._nDataBytes * 8;
	            var nBitsLeft = data.sigBytes * 8;

	            // Add padding
	            dataWords[nBitsLeft >>> 5] |= 0x80 << (24 - nBitsLeft % 32);
	            dataWords[(((nBitsLeft + 64) >>> 9) << 4) + 14] = (
	                (((nBitsTotal << 8)  | (nBitsTotal >>> 24)) & 0x00ff00ff) |
	                (((nBitsTotal << 24) | (nBitsTotal >>> 8))  & 0xff00ff00)
	            );
	            data.sigBytes = (dataWords.length + 1) * 4;

	            // Hash final blocks
	            this._process();

	            // Shortcuts
	            var hash = this._hash;
	            var H = hash.words;

	            // Swap endian
	            for (var i = 0; i < 5; i++) {
	                // Shortcut
	                var H_i = H[i];

	                // Swap
	                H[i] = (((H_i << 8)  | (H_i >>> 24)) & 0x00ff00ff) |
	                       (((H_i << 24) | (H_i >>> 8))  & 0xff00ff00);
	            }

	            // Return final computed hash
	            return hash;
	        },

	        clone: function () {
	            var clone = Hasher.clone.call(this);
	            clone._hash = this._hash.clone();

	            return clone;
	        }
	    });


	    function f1(x, y, z) {
	        return ((x) ^ (y) ^ (z));

	    }

	    function f2(x, y, z) {
	        return (((x)&(y)) | ((~x)&(z)));
	    }

	    function f3(x, y, z) {
	        return (((x) | (~(y))) ^ (z));
	    }

	    function f4(x, y, z) {
	        return (((x) & (z)) | ((y)&(~(z))));
	    }

	    function f5(x, y, z) {
	        return ((x) ^ ((y) |(~(z))));

	    }

	    function rotl(x,n) {
	        return (x<<n) | (x>>>(32-n));
	    }


	    /**
	     * Shortcut function to the hasher's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     *
	     * @return {WordArray} The hash.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hash = CryptoJS.RIPEMD160('message');
	     *     var hash = CryptoJS.RIPEMD160(wordArray);
	     */
	    C.RIPEMD160 = Hasher._createHelper(RIPEMD160);

	    /**
	     * Shortcut function to the HMAC's object interface.
	     *
	     * @param {WordArray|string} message The message to hash.
	     * @param {WordArray|string} key The secret key.
	     *
	     * @return {WordArray} The HMAC.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var hmac = CryptoJS.HmacRIPEMD160(message, key);
	     */
	    C.HmacRIPEMD160 = Hasher._createHmacHelper(RIPEMD160);
	}(Math));


	return CryptoJS.RIPEMD160;

}));

/***/ }),
/* 369 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(167), __webpack_require__(168));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var Base = C_lib.Base;
	    var WordArray = C_lib.WordArray;
	    var C_algo = C.algo;
	    var SHA1 = C_algo.SHA1;
	    var HMAC = C_algo.HMAC;

	    /**
	     * Password-Based Key Derivation Function 2 algorithm.
	     */
	    var PBKDF2 = C_algo.PBKDF2 = Base.extend({
	        /**
	         * Configuration options.
	         *
	         * @property {number} keySize The key size in words to generate. Default: 4 (128 bits)
	         * @property {Hasher} hasher The hasher to use. Default: SHA1
	         * @property {number} iterations The number of iterations to perform. Default: 1
	         */
	        cfg: Base.extend({
	            keySize: 128/32,
	            hasher: SHA1,
	            iterations: 1
	        }),

	        /**
	         * Initializes a newly created key derivation function.
	         *
	         * @param {Object} cfg (Optional) The configuration options to use for the derivation.
	         *
	         * @example
	         *
	         *     var kdf = CryptoJS.algo.PBKDF2.create();
	         *     var kdf = CryptoJS.algo.PBKDF2.create({ keySize: 8 });
	         *     var kdf = CryptoJS.algo.PBKDF2.create({ keySize: 8, iterations: 1000 });
	         */
	        init: function (cfg) {
	            this.cfg = this.cfg.extend(cfg);
	        },

	        /**
	         * Computes the Password-Based Key Derivation Function 2.
	         *
	         * @param {WordArray|string} password The password.
	         * @param {WordArray|string} salt A salt.
	         *
	         * @return {WordArray} The derived key.
	         *
	         * @example
	         *
	         *     var key = kdf.compute(password, salt);
	         */
	        compute: function (password, salt) {
	            // Shortcut
	            var cfg = this.cfg;

	            // Init HMAC
	            var hmac = HMAC.create(cfg.hasher, password);

	            // Initial values
	            var derivedKey = WordArray.create();
	            var blockIndex = WordArray.create([0x00000001]);

	            // Shortcuts
	            var derivedKeyWords = derivedKey.words;
	            var blockIndexWords = blockIndex.words;
	            var keySize = cfg.keySize;
	            var iterations = cfg.iterations;

	            // Generate key
	            while (derivedKeyWords.length < keySize) {
	                var block = hmac.update(salt).finalize(blockIndex);
	                hmac.reset();

	                // Shortcuts
	                var blockWords = block.words;
	                var blockWordsLength = blockWords.length;

	                // Iterations
	                var intermediate = block;
	                for (var i = 1; i < iterations; i++) {
	                    intermediate = hmac.finalize(intermediate);
	                    hmac.reset();

	                    // Shortcut
	                    var intermediateWords = intermediate.words;

	                    // XOR intermediate with block
	                    for (var j = 0; j < blockWordsLength; j++) {
	                        blockWords[j] ^= intermediateWords[j];
	                    }
	                }

	                derivedKey.concat(block);
	                blockIndexWords[0]++;
	            }
	            derivedKey.sigBytes = keySize * 4;

	            return derivedKey;
	        }
	    });

	    /**
	     * Computes the Password-Based Key Derivation Function 2.
	     *
	     * @param {WordArray|string} password The password.
	     * @param {WordArray|string} salt A salt.
	     * @param {Object} cfg (Optional) The configuration options to use for this computation.
	     *
	     * @return {WordArray} The derived key.
	     *
	     * @static
	     *
	     * @example
	     *
	     *     var key = CryptoJS.PBKDF2(password, salt);
	     *     var key = CryptoJS.PBKDF2(password, salt, { keySize: 8 });
	     *     var key = CryptoJS.PBKDF2(password, salt, { keySize: 8, iterations: 1000 });
	     */
	    C.PBKDF2 = function (password, salt, cfg) {
	        return PBKDF2.create(cfg).compute(password, salt);
	    };
	}());


	return CryptoJS.PBKDF2;

}));

/***/ }),
/* 370 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	/**
	 * Cipher Feedback block mode.
	 */
	CryptoJS.mode.CFB = (function () {
	    var CFB = CryptoJS.lib.BlockCipherMode.extend();

	    CFB.Encryptor = CFB.extend({
	        processBlock: function (words, offset) {
	            // Shortcuts
	            var cipher = this._cipher;
	            var blockSize = cipher.blockSize;

	            generateKeystreamAndEncrypt.call(this, words, offset, blockSize, cipher);

	            // Remember this block to use with next block
	            this._prevBlock = words.slice(offset, offset + blockSize);
	        }
	    });

	    CFB.Decryptor = CFB.extend({
	        processBlock: function (words, offset) {
	            // Shortcuts
	            var cipher = this._cipher;
	            var blockSize = cipher.blockSize;

	            // Remember this block to use with next block
	            var thisBlock = words.slice(offset, offset + blockSize);

	            generateKeystreamAndEncrypt.call(this, words, offset, blockSize, cipher);

	            // This block becomes the previous block
	            this._prevBlock = thisBlock;
	        }
	    });

	    function generateKeystreamAndEncrypt(words, offset, blockSize, cipher) {
	        // Shortcut
	        var iv = this._iv;

	        // Generate keystream
	        if (iv) {
	            var keystream = iv.slice(0);

	            // Remove IV for subsequent blocks
	            this._iv = undefined;
	        } else {
	            var keystream = this._prevBlock;
	        }
	        cipher.encryptBlock(keystream, 0);

	        // Encrypt
	        for (var i = 0; i < blockSize; i++) {
	            words[offset + i] ^= keystream[i];
	        }
	    }

	    return CFB;
	}());


	return CryptoJS.mode.CFB;

}));

/***/ }),
/* 371 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	/**
	 * Counter block mode.
	 */
	CryptoJS.mode.CTR = (function () {
	    var CTR = CryptoJS.lib.BlockCipherMode.extend();

	    var Encryptor = CTR.Encryptor = CTR.extend({
	        processBlock: function (words, offset) {
	            // Shortcuts
	            var cipher = this._cipher
	            var blockSize = cipher.blockSize;
	            var iv = this._iv;
	            var counter = this._counter;

	            // Generate keystream
	            if (iv) {
	                counter = this._counter = iv.slice(0);

	                // Remove IV for subsequent blocks
	                this._iv = undefined;
	            }
	            var keystream = counter.slice(0);
	            cipher.encryptBlock(keystream, 0);

	            // Increment counter
	            counter[blockSize - 1] = (counter[blockSize - 1] + 1) | 0

	            // Encrypt
	            for (var i = 0; i < blockSize; i++) {
	                words[offset + i] ^= keystream[i];
	            }
	        }
	    });

	    CTR.Decryptor = Encryptor;

	    return CTR;
	}());


	return CryptoJS.mode.CTR;

}));

/***/ }),
/* 372 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	/** @preserve
	 * Counter block mode compatible with  Dr Brian Gladman fileenc.c
	 * derived from CryptoJS.mode.CTR
	 * Jan Hruby jhruby.web@gmail.com
	 */
	CryptoJS.mode.CTRGladman = (function () {
	    var CTRGladman = CryptoJS.lib.BlockCipherMode.extend();

		function incWord(word)
		{
			if (((word >> 24) & 0xff) === 0xff) { //overflow
			var b1 = (word >> 16)&0xff;
			var b2 = (word >> 8)&0xff;
			var b3 = word & 0xff;

			if (b1 === 0xff) // overflow b1
			{
			b1 = 0;
			if (b2 === 0xff)
			{
				b2 = 0;
				if (b3 === 0xff)
				{
					b3 = 0;
				}
				else
				{
					++b3;
				}
			}
			else
			{
				++b2;
			}
			}
			else
			{
			++b1;
			}

			word = 0;
			word += (b1 << 16);
			word += (b2 << 8);
			word += b3;
			}
			else
			{
			word += (0x01 << 24);
			}
			return word;
		}

		function incCounter(counter)
		{
			if ((counter[0] = incWord(counter[0])) === 0)
			{
				// encr_data in fileenc.c from  Dr Brian Gladman's counts only with DWORD j < 8
				counter[1] = incWord(counter[1]);
			}
			return counter;
		}

	    var Encryptor = CTRGladman.Encryptor = CTRGladman.extend({
	        processBlock: function (words, offset) {
	            // Shortcuts
	            var cipher = this._cipher
	            var blockSize = cipher.blockSize;
	            var iv = this._iv;
	            var counter = this._counter;

	            // Generate keystream
	            if (iv) {
	                counter = this._counter = iv.slice(0);

	                // Remove IV for subsequent blocks
	                this._iv = undefined;
	            }

				incCounter(counter);

				var keystream = counter.slice(0);
	            cipher.encryptBlock(keystream, 0);

	            // Encrypt
	            for (var i = 0; i < blockSize; i++) {
	                words[offset + i] ^= keystream[i];
	            }
	        }
	    });

	    CTRGladman.Decryptor = Encryptor;

	    return CTRGladman;
	}());




	return CryptoJS.mode.CTRGladman;

}));

/***/ }),
/* 373 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	/**
	 * Output Feedback block mode.
	 */
	CryptoJS.mode.OFB = (function () {
	    var OFB = CryptoJS.lib.BlockCipherMode.extend();

	    var Encryptor = OFB.Encryptor = OFB.extend({
	        processBlock: function (words, offset) {
	            // Shortcuts
	            var cipher = this._cipher
	            var blockSize = cipher.blockSize;
	            var iv = this._iv;
	            var keystream = this._keystream;

	            // Generate keystream
	            if (iv) {
	                keystream = this._keystream = iv.slice(0);

	                // Remove IV for subsequent blocks
	                this._iv = undefined;
	            }
	            cipher.encryptBlock(keystream, 0);

	            // Encrypt
	            for (var i = 0; i < blockSize; i++) {
	                words[offset + i] ^= keystream[i];
	            }
	        }
	    });

	    OFB.Decryptor = Encryptor;

	    return OFB;
	}());


	return CryptoJS.mode.OFB;

}));

/***/ }),
/* 374 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	/**
	 * Electronic Codebook block mode.
	 */
	CryptoJS.mode.ECB = (function () {
	    var ECB = CryptoJS.lib.BlockCipherMode.extend();

	    ECB.Encryptor = ECB.extend({
	        processBlock: function (words, offset) {
	            this._cipher.encryptBlock(words, offset);
	        }
	    });

	    ECB.Decryptor = ECB.extend({
	        processBlock: function (words, offset) {
	            this._cipher.decryptBlock(words, offset);
	        }
	    });

	    return ECB;
	}());


	return CryptoJS.mode.ECB;

}));

/***/ }),
/* 375 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	/**
	 * ANSI X.923 padding strategy.
	 */
	CryptoJS.pad.AnsiX923 = {
	    pad: function (data, blockSize) {
	        // Shortcuts
	        var dataSigBytes = data.sigBytes;
	        var blockSizeBytes = blockSize * 4;

	        // Count padding bytes
	        var nPaddingBytes = blockSizeBytes - dataSigBytes % blockSizeBytes;

	        // Compute last byte position
	        var lastBytePos = dataSigBytes + nPaddingBytes - 1;

	        // Pad
	        data.clamp();
	        data.words[lastBytePos >>> 2] |= nPaddingBytes << (24 - (lastBytePos % 4) * 8);
	        data.sigBytes += nPaddingBytes;
	    },

	    unpad: function (data) {
	        // Get number of padding bytes from last byte
	        var nPaddingBytes = data.words[(data.sigBytes - 1) >>> 2] & 0xff;

	        // Remove padding
	        data.sigBytes -= nPaddingBytes;
	    }
	};


	return CryptoJS.pad.Ansix923;

}));

/***/ }),
/* 376 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	/**
	 * ISO 10126 padding strategy.
	 */
	CryptoJS.pad.Iso10126 = {
	    pad: function (data, blockSize) {
	        // Shortcut
	        var blockSizeBytes = blockSize * 4;

	        // Count padding bytes
	        var nPaddingBytes = blockSizeBytes - data.sigBytes % blockSizeBytes;

	        // Pad
	        data.concat(CryptoJS.lib.WordArray.random(nPaddingBytes - 1)).
	             concat(CryptoJS.lib.WordArray.create([nPaddingBytes << 24], 1));
	    },

	    unpad: function (data) {
	        // Get number of padding bytes from last byte
	        var nPaddingBytes = data.words[(data.sigBytes - 1) >>> 2] & 0xff;

	        // Remove padding
	        data.sigBytes -= nPaddingBytes;
	    }
	};


	return CryptoJS.pad.Iso10126;

}));

/***/ }),
/* 377 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	/**
	 * ISO/IEC 9797-1 Padding Method 2.
	 */
	CryptoJS.pad.Iso97971 = {
	    pad: function (data, blockSize) {
	        // Add 0x80 byte
	        data.concat(CryptoJS.lib.WordArray.create([0x80000000], 1));

	        // Zero pad the rest
	        CryptoJS.pad.ZeroPadding.pad(data, blockSize);
	    },

	    unpad: function (data) {
	        // Remove zero padding
	        CryptoJS.pad.ZeroPadding.unpad(data);

	        // Remove one more byte -- the 0x80 byte
	        data.sigBytes--;
	    }
	};


	return CryptoJS.pad.Iso97971;

}));

/***/ }),
/* 378 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	/**
	 * Zero padding strategy.
	 */
	CryptoJS.pad.ZeroPadding = {
	    pad: function (data, blockSize) {
	        // Shortcut
	        var blockSizeBytes = blockSize * 4;

	        // Pad
	        data.clamp();
	        data.sigBytes += blockSizeBytes - ((data.sigBytes % blockSizeBytes) || blockSizeBytes);
	    },

	    unpad: function (data) {
	        // Shortcut
	        var dataWords = data.words;

	        // Unpad
	        var i = data.sigBytes - 1;
	        while (!((dataWords[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff)) {
	            i--;
	        }
	        data.sigBytes = i + 1;
	    }
	};


	return CryptoJS.pad.ZeroPadding;

}));

/***/ }),
/* 379 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	/**
	 * A noop padding strategy.
	 */
	CryptoJS.pad.NoPadding = {
	    pad: function () {
	    },

	    unpad: function () {
	    }
	};


	return CryptoJS.pad.NoPadding;

}));

/***/ }),
/* 380 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	(function (undefined) {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var CipherParams = C_lib.CipherParams;
	    var C_enc = C.enc;
	    var Hex = C_enc.Hex;
	    var C_format = C.format;

	    var HexFormatter = C_format.Hex = {
	        /**
	         * Converts the ciphertext of a cipher params object to a hexadecimally encoded string.
	         *
	         * @param {CipherParams} cipherParams The cipher params object.
	         *
	         * @return {string} The hexadecimally encoded string.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var hexString = CryptoJS.format.Hex.stringify(cipherParams);
	         */
	        stringify: function (cipherParams) {
	            return cipherParams.ciphertext.toString(Hex);
	        },

	        /**
	         * Converts a hexadecimally encoded ciphertext string to a cipher params object.
	         *
	         * @param {string} input The hexadecimally encoded string.
	         *
	         * @return {CipherParams} The cipher params object.
	         *
	         * @static
	         *
	         * @example
	         *
	         *     var cipherParams = CryptoJS.format.Hex.parse(hexString);
	         */
	        parse: function (input) {
	            var ciphertext = Hex.parse(input);
	            return CipherParams.create({ ciphertext: ciphertext });
	        }
	    };
	}());


	return CryptoJS.format.Hex;

}));

/***/ }),
/* 381 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(87), __webpack_require__(88), __webpack_require__(49), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var BlockCipher = C_lib.BlockCipher;
	    var C_algo = C.algo;

	    // Lookup tables
	    var SBOX = [];
	    var INV_SBOX = [];
	    var SUB_MIX_0 = [];
	    var SUB_MIX_1 = [];
	    var SUB_MIX_2 = [];
	    var SUB_MIX_3 = [];
	    var INV_SUB_MIX_0 = [];
	    var INV_SUB_MIX_1 = [];
	    var INV_SUB_MIX_2 = [];
	    var INV_SUB_MIX_3 = [];

	    // Compute lookup tables
	    (function () {
	        // Compute double table
	        var d = [];
	        for (var i = 0; i < 256; i++) {
	            if (i < 128) {
	                d[i] = i << 1;
	            } else {
	                d[i] = (i << 1) ^ 0x11b;
	            }
	        }

	        // Walk GF(2^8)
	        var x = 0;
	        var xi = 0;
	        for (var i = 0; i < 256; i++) {
	            // Compute sbox
	            var sx = xi ^ (xi << 1) ^ (xi << 2) ^ (xi << 3) ^ (xi << 4);
	            sx = (sx >>> 8) ^ (sx & 0xff) ^ 0x63;
	            SBOX[x] = sx;
	            INV_SBOX[sx] = x;

	            // Compute multiplication
	            var x2 = d[x];
	            var x4 = d[x2];
	            var x8 = d[x4];

	            // Compute sub bytes, mix columns tables
	            var t = (d[sx] * 0x101) ^ (sx * 0x1010100);
	            SUB_MIX_0[x] = (t << 24) | (t >>> 8);
	            SUB_MIX_1[x] = (t << 16) | (t >>> 16);
	            SUB_MIX_2[x] = (t << 8)  | (t >>> 24);
	            SUB_MIX_3[x] = t;

	            // Compute inv sub bytes, inv mix columns tables
	            var t = (x8 * 0x1010101) ^ (x4 * 0x10001) ^ (x2 * 0x101) ^ (x * 0x1010100);
	            INV_SUB_MIX_0[sx] = (t << 24) | (t >>> 8);
	            INV_SUB_MIX_1[sx] = (t << 16) | (t >>> 16);
	            INV_SUB_MIX_2[sx] = (t << 8)  | (t >>> 24);
	            INV_SUB_MIX_3[sx] = t;

	            // Compute next counter
	            if (!x) {
	                x = xi = 1;
	            } else {
	                x = x2 ^ d[d[d[x8 ^ x2]]];
	                xi ^= d[d[xi]];
	            }
	        }
	    }());

	    // Precomputed Rcon lookup
	    var RCON = [0x00, 0x01, 0x02, 0x04, 0x08, 0x10, 0x20, 0x40, 0x80, 0x1b, 0x36];

	    /**
	     * AES block cipher algorithm.
	     */
	    var AES = C_algo.AES = BlockCipher.extend({
	        _doReset: function () {
	            // Skip reset of nRounds has been set before and key did not change
	            if (this._nRounds && this._keyPriorReset === this._key) {
	                return;
	            }

	            // Shortcuts
	            var key = this._keyPriorReset = this._key;
	            var keyWords = key.words;
	            var keySize = key.sigBytes / 4;

	            // Compute number of rounds
	            var nRounds = this._nRounds = keySize + 6;

	            // Compute number of key schedule rows
	            var ksRows = (nRounds + 1) * 4;

	            // Compute key schedule
	            var keySchedule = this._keySchedule = [];
	            for (var ksRow = 0; ksRow < ksRows; ksRow++) {
	                if (ksRow < keySize) {
	                    keySchedule[ksRow] = keyWords[ksRow];
	                } else {
	                    var t = keySchedule[ksRow - 1];

	                    if (!(ksRow % keySize)) {
	                        // Rot word
	                        t = (t << 8) | (t >>> 24);

	                        // Sub word
	                        t = (SBOX[t >>> 24] << 24) | (SBOX[(t >>> 16) & 0xff] << 16) | (SBOX[(t >>> 8) & 0xff] << 8) | SBOX[t & 0xff];

	                        // Mix Rcon
	                        t ^= RCON[(ksRow / keySize) | 0] << 24;
	                    } else if (keySize > 6 && ksRow % keySize == 4) {
	                        // Sub word
	                        t = (SBOX[t >>> 24] << 24) | (SBOX[(t >>> 16) & 0xff] << 16) | (SBOX[(t >>> 8) & 0xff] << 8) | SBOX[t & 0xff];
	                    }

	                    keySchedule[ksRow] = keySchedule[ksRow - keySize] ^ t;
	                }
	            }

	            // Compute inv key schedule
	            var invKeySchedule = this._invKeySchedule = [];
	            for (var invKsRow = 0; invKsRow < ksRows; invKsRow++) {
	                var ksRow = ksRows - invKsRow;

	                if (invKsRow % 4) {
	                    var t = keySchedule[ksRow];
	                } else {
	                    var t = keySchedule[ksRow - 4];
	                }

	                if (invKsRow < 4 || ksRow <= 4) {
	                    invKeySchedule[invKsRow] = t;
	                } else {
	                    invKeySchedule[invKsRow] = INV_SUB_MIX_0[SBOX[t >>> 24]] ^ INV_SUB_MIX_1[SBOX[(t >>> 16) & 0xff]] ^
	                                               INV_SUB_MIX_2[SBOX[(t >>> 8) & 0xff]] ^ INV_SUB_MIX_3[SBOX[t & 0xff]];
	                }
	            }
	        },

	        encryptBlock: function (M, offset) {
	            this._doCryptBlock(M, offset, this._keySchedule, SUB_MIX_0, SUB_MIX_1, SUB_MIX_2, SUB_MIX_3, SBOX);
	        },

	        decryptBlock: function (M, offset) {
	            // Swap 2nd and 4th rows
	            var t = M[offset + 1];
	            M[offset + 1] = M[offset + 3];
	            M[offset + 3] = t;

	            this._doCryptBlock(M, offset, this._invKeySchedule, INV_SUB_MIX_0, INV_SUB_MIX_1, INV_SUB_MIX_2, INV_SUB_MIX_3, INV_SBOX);

	            // Inv swap 2nd and 4th rows
	            var t = M[offset + 1];
	            M[offset + 1] = M[offset + 3];
	            M[offset + 3] = t;
	        },

	        _doCryptBlock: function (M, offset, keySchedule, SUB_MIX_0, SUB_MIX_1, SUB_MIX_2, SUB_MIX_3, SBOX) {
	            // Shortcut
	            var nRounds = this._nRounds;

	            // Get input, add round key
	            var s0 = M[offset]     ^ keySchedule[0];
	            var s1 = M[offset + 1] ^ keySchedule[1];
	            var s2 = M[offset + 2] ^ keySchedule[2];
	            var s3 = M[offset + 3] ^ keySchedule[3];

	            // Key schedule row counter
	            var ksRow = 4;

	            // Rounds
	            for (var round = 1; round < nRounds; round++) {
	                // Shift rows, sub bytes, mix columns, add round key
	                var t0 = SUB_MIX_0[s0 >>> 24] ^ SUB_MIX_1[(s1 >>> 16) & 0xff] ^ SUB_MIX_2[(s2 >>> 8) & 0xff] ^ SUB_MIX_3[s3 & 0xff] ^ keySchedule[ksRow++];
	                var t1 = SUB_MIX_0[s1 >>> 24] ^ SUB_MIX_1[(s2 >>> 16) & 0xff] ^ SUB_MIX_2[(s3 >>> 8) & 0xff] ^ SUB_MIX_3[s0 & 0xff] ^ keySchedule[ksRow++];
	                var t2 = SUB_MIX_0[s2 >>> 24] ^ SUB_MIX_1[(s3 >>> 16) & 0xff] ^ SUB_MIX_2[(s0 >>> 8) & 0xff] ^ SUB_MIX_3[s1 & 0xff] ^ keySchedule[ksRow++];
	                var t3 = SUB_MIX_0[s3 >>> 24] ^ SUB_MIX_1[(s0 >>> 16) & 0xff] ^ SUB_MIX_2[(s1 >>> 8) & 0xff] ^ SUB_MIX_3[s2 & 0xff] ^ keySchedule[ksRow++];

	                // Update state
	                s0 = t0;
	                s1 = t1;
	                s2 = t2;
	                s3 = t3;
	            }

	            // Shift rows, sub bytes, add round key
	            var t0 = ((SBOX[s0 >>> 24] << 24) | (SBOX[(s1 >>> 16) & 0xff] << 16) | (SBOX[(s2 >>> 8) & 0xff] << 8) | SBOX[s3 & 0xff]) ^ keySchedule[ksRow++];
	            var t1 = ((SBOX[s1 >>> 24] << 24) | (SBOX[(s2 >>> 16) & 0xff] << 16) | (SBOX[(s3 >>> 8) & 0xff] << 8) | SBOX[s0 & 0xff]) ^ keySchedule[ksRow++];
	            var t2 = ((SBOX[s2 >>> 24] << 24) | (SBOX[(s3 >>> 16) & 0xff] << 16) | (SBOX[(s0 >>> 8) & 0xff] << 8) | SBOX[s1 & 0xff]) ^ keySchedule[ksRow++];
	            var t3 = ((SBOX[s3 >>> 24] << 24) | (SBOX[(s0 >>> 16) & 0xff] << 16) | (SBOX[(s1 >>> 8) & 0xff] << 8) | SBOX[s2 & 0xff]) ^ keySchedule[ksRow++];

	            // Set output
	            M[offset]     = t0;
	            M[offset + 1] = t1;
	            M[offset + 2] = t2;
	            M[offset + 3] = t3;
	        },

	        keySize: 256/32
	    });

	    /**
	     * Shortcut functions to the cipher's object interface.
	     *
	     * @example
	     *
	     *     var ciphertext = CryptoJS.AES.encrypt(message, key, cfg);
	     *     var plaintext  = CryptoJS.AES.decrypt(ciphertext, key, cfg);
	     */
	    C.AES = BlockCipher._createHelper(AES);
	}());


	return CryptoJS.AES;

}));

/***/ }),
/* 382 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(87), __webpack_require__(88), __webpack_require__(49), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var WordArray = C_lib.WordArray;
	    var BlockCipher = C_lib.BlockCipher;
	    var C_algo = C.algo;

	    // Permuted Choice 1 constants
	    var PC1 = [
	        57, 49, 41, 33, 25, 17, 9,  1,
	        58, 50, 42, 34, 26, 18, 10, 2,
	        59, 51, 43, 35, 27, 19, 11, 3,
	        60, 52, 44, 36, 63, 55, 47, 39,
	        31, 23, 15, 7,  62, 54, 46, 38,
	        30, 22, 14, 6,  61, 53, 45, 37,
	        29, 21, 13, 5,  28, 20, 12, 4
	    ];

	    // Permuted Choice 2 constants
	    var PC2 = [
	        14, 17, 11, 24, 1,  5,
	        3,  28, 15, 6,  21, 10,
	        23, 19, 12, 4,  26, 8,
	        16, 7,  27, 20, 13, 2,
	        41, 52, 31, 37, 47, 55,
	        30, 40, 51, 45, 33, 48,
	        44, 49, 39, 56, 34, 53,
	        46, 42, 50, 36, 29, 32
	    ];

	    // Cumulative bit shift constants
	    var BIT_SHIFTS = [1,  2,  4,  6,  8,  10, 12, 14, 15, 17, 19, 21, 23, 25, 27, 28];

	    // SBOXes and round permutation constants
	    var SBOX_P = [
	        {
	            0x0: 0x808200,
	            0x10000000: 0x8000,
	            0x20000000: 0x808002,
	            0x30000000: 0x2,
	            0x40000000: 0x200,
	            0x50000000: 0x808202,
	            0x60000000: 0x800202,
	            0x70000000: 0x800000,
	            0x80000000: 0x202,
	            0x90000000: 0x800200,
	            0xa0000000: 0x8200,
	            0xb0000000: 0x808000,
	            0xc0000000: 0x8002,
	            0xd0000000: 0x800002,
	            0xe0000000: 0x0,
	            0xf0000000: 0x8202,
	            0x8000000: 0x0,
	            0x18000000: 0x808202,
	            0x28000000: 0x8202,
	            0x38000000: 0x8000,
	            0x48000000: 0x808200,
	            0x58000000: 0x200,
	            0x68000000: 0x808002,
	            0x78000000: 0x2,
	            0x88000000: 0x800200,
	            0x98000000: 0x8200,
	            0xa8000000: 0x808000,
	            0xb8000000: 0x800202,
	            0xc8000000: 0x800002,
	            0xd8000000: 0x8002,
	            0xe8000000: 0x202,
	            0xf8000000: 0x800000,
	            0x1: 0x8000,
	            0x10000001: 0x2,
	            0x20000001: 0x808200,
	            0x30000001: 0x800000,
	            0x40000001: 0x808002,
	            0x50000001: 0x8200,
	            0x60000001: 0x200,
	            0x70000001: 0x800202,
	            0x80000001: 0x808202,
	            0x90000001: 0x808000,
	            0xa0000001: 0x800002,
	            0xb0000001: 0x8202,
	            0xc0000001: 0x202,
	            0xd0000001: 0x800200,
	            0xe0000001: 0x8002,
	            0xf0000001: 0x0,
	            0x8000001: 0x808202,
	            0x18000001: 0x808000,
	            0x28000001: 0x800000,
	            0x38000001: 0x200,
	            0x48000001: 0x8000,
	            0x58000001: 0x800002,
	            0x68000001: 0x2,
	            0x78000001: 0x8202,
	            0x88000001: 0x8002,
	            0x98000001: 0x800202,
	            0xa8000001: 0x202,
	            0xb8000001: 0x808200,
	            0xc8000001: 0x800200,
	            0xd8000001: 0x0,
	            0xe8000001: 0x8200,
	            0xf8000001: 0x808002
	        },
	        {
	            0x0: 0x40084010,
	            0x1000000: 0x4000,
	            0x2000000: 0x80000,
	            0x3000000: 0x40080010,
	            0x4000000: 0x40000010,
	            0x5000000: 0x40084000,
	            0x6000000: 0x40004000,
	            0x7000000: 0x10,
	            0x8000000: 0x84000,
	            0x9000000: 0x40004010,
	            0xa000000: 0x40000000,
	            0xb000000: 0x84010,
	            0xc000000: 0x80010,
	            0xd000000: 0x0,
	            0xe000000: 0x4010,
	            0xf000000: 0x40080000,
	            0x800000: 0x40004000,
	            0x1800000: 0x84010,
	            0x2800000: 0x10,
	            0x3800000: 0x40004010,
	            0x4800000: 0x40084010,
	            0x5800000: 0x40000000,
	            0x6800000: 0x80000,
	            0x7800000: 0x40080010,
	            0x8800000: 0x80010,
	            0x9800000: 0x0,
	            0xa800000: 0x4000,
	            0xb800000: 0x40080000,
	            0xc800000: 0x40000010,
	            0xd800000: 0x84000,
	            0xe800000: 0x40084000,
	            0xf800000: 0x4010,
	            0x10000000: 0x0,
	            0x11000000: 0x40080010,
	            0x12000000: 0x40004010,
	            0x13000000: 0x40084000,
	            0x14000000: 0x40080000,
	            0x15000000: 0x10,
	            0x16000000: 0x84010,
	            0x17000000: 0x4000,
	            0x18000000: 0x4010,
	            0x19000000: 0x80000,
	            0x1a000000: 0x80010,
	            0x1b000000: 0x40000010,
	            0x1c000000: 0x84000,
	            0x1d000000: 0x40004000,
	            0x1e000000: 0x40000000,
	            0x1f000000: 0x40084010,
	            0x10800000: 0x84010,
	            0x11800000: 0x80000,
	            0x12800000: 0x40080000,
	            0x13800000: 0x4000,
	            0x14800000: 0x40004000,
	            0x15800000: 0x40084010,
	            0x16800000: 0x10,
	            0x17800000: 0x40000000,
	            0x18800000: 0x40084000,
	            0x19800000: 0x40000010,
	            0x1a800000: 0x40004010,
	            0x1b800000: 0x80010,
	            0x1c800000: 0x0,
	            0x1d800000: 0x4010,
	            0x1e800000: 0x40080010,
	            0x1f800000: 0x84000
	        },
	        {
	            0x0: 0x104,
	            0x100000: 0x0,
	            0x200000: 0x4000100,
	            0x300000: 0x10104,
	            0x400000: 0x10004,
	            0x500000: 0x4000004,
	            0x600000: 0x4010104,
	            0x700000: 0x4010000,
	            0x800000: 0x4000000,
	            0x900000: 0x4010100,
	            0xa00000: 0x10100,
	            0xb00000: 0x4010004,
	            0xc00000: 0x4000104,
	            0xd00000: 0x10000,
	            0xe00000: 0x4,
	            0xf00000: 0x100,
	            0x80000: 0x4010100,
	            0x180000: 0x4010004,
	            0x280000: 0x0,
	            0x380000: 0x4000100,
	            0x480000: 0x4000004,
	            0x580000: 0x10000,
	            0x680000: 0x10004,
	            0x780000: 0x104,
	            0x880000: 0x4,
	            0x980000: 0x100,
	            0xa80000: 0x4010000,
	            0xb80000: 0x10104,
	            0xc80000: 0x10100,
	            0xd80000: 0x4000104,
	            0xe80000: 0x4010104,
	            0xf80000: 0x4000000,
	            0x1000000: 0x4010100,
	            0x1100000: 0x10004,
	            0x1200000: 0x10000,
	            0x1300000: 0x4000100,
	            0x1400000: 0x100,
	            0x1500000: 0x4010104,
	            0x1600000: 0x4000004,
	            0x1700000: 0x0,
	            0x1800000: 0x4000104,
	            0x1900000: 0x4000000,
	            0x1a00000: 0x4,
	            0x1b00000: 0x10100,
	            0x1c00000: 0x4010000,
	            0x1d00000: 0x104,
	            0x1e00000: 0x10104,
	            0x1f00000: 0x4010004,
	            0x1080000: 0x4000000,
	            0x1180000: 0x104,
	            0x1280000: 0x4010100,
	            0x1380000: 0x0,
	            0x1480000: 0x10004,
	            0x1580000: 0x4000100,
	            0x1680000: 0x100,
	            0x1780000: 0x4010004,
	            0x1880000: 0x10000,
	            0x1980000: 0x4010104,
	            0x1a80000: 0x10104,
	            0x1b80000: 0x4000004,
	            0x1c80000: 0x4000104,
	            0x1d80000: 0x4010000,
	            0x1e80000: 0x4,
	            0x1f80000: 0x10100
	        },
	        {
	            0x0: 0x80401000,
	            0x10000: 0x80001040,
	            0x20000: 0x401040,
	            0x30000: 0x80400000,
	            0x40000: 0x0,
	            0x50000: 0x401000,
	            0x60000: 0x80000040,
	            0x70000: 0x400040,
	            0x80000: 0x80000000,
	            0x90000: 0x400000,
	            0xa0000: 0x40,
	            0xb0000: 0x80001000,
	            0xc0000: 0x80400040,
	            0xd0000: 0x1040,
	            0xe0000: 0x1000,
	            0xf0000: 0x80401040,
	            0x8000: 0x80001040,
	            0x18000: 0x40,
	            0x28000: 0x80400040,
	            0x38000: 0x80001000,
	            0x48000: 0x401000,
	            0x58000: 0x80401040,
	            0x68000: 0x0,
	            0x78000: 0x80400000,
	            0x88000: 0x1000,
	            0x98000: 0x80401000,
	            0xa8000: 0x400000,
	            0xb8000: 0x1040,
	            0xc8000: 0x80000000,
	            0xd8000: 0x400040,
	            0xe8000: 0x401040,
	            0xf8000: 0x80000040,
	            0x100000: 0x400040,
	            0x110000: 0x401000,
	            0x120000: 0x80000040,
	            0x130000: 0x0,
	            0x140000: 0x1040,
	            0x150000: 0x80400040,
	            0x160000: 0x80401000,
	            0x170000: 0x80001040,
	            0x180000: 0x80401040,
	            0x190000: 0x80000000,
	            0x1a0000: 0x80400000,
	            0x1b0000: 0x401040,
	            0x1c0000: 0x80001000,
	            0x1d0000: 0x400000,
	            0x1e0000: 0x40,
	            0x1f0000: 0x1000,
	            0x108000: 0x80400000,
	            0x118000: 0x80401040,
	            0x128000: 0x0,
	            0x138000: 0x401000,
	            0x148000: 0x400040,
	            0x158000: 0x80000000,
	            0x168000: 0x80001040,
	            0x178000: 0x40,
	            0x188000: 0x80000040,
	            0x198000: 0x1000,
	            0x1a8000: 0x80001000,
	            0x1b8000: 0x80400040,
	            0x1c8000: 0x1040,
	            0x1d8000: 0x80401000,
	            0x1e8000: 0x400000,
	            0x1f8000: 0x401040
	        },
	        {
	            0x0: 0x80,
	            0x1000: 0x1040000,
	            0x2000: 0x40000,
	            0x3000: 0x20000000,
	            0x4000: 0x20040080,
	            0x5000: 0x1000080,
	            0x6000: 0x21000080,
	            0x7000: 0x40080,
	            0x8000: 0x1000000,
	            0x9000: 0x20040000,
	            0xa000: 0x20000080,
	            0xb000: 0x21040080,
	            0xc000: 0x21040000,
	            0xd000: 0x0,
	            0xe000: 0x1040080,
	            0xf000: 0x21000000,
	            0x800: 0x1040080,
	            0x1800: 0x21000080,
	            0x2800: 0x80,
	            0x3800: 0x1040000,
	            0x4800: 0x40000,
	            0x5800: 0x20040080,
	            0x6800: 0x21040000,
	            0x7800: 0x20000000,
	            0x8800: 0x20040000,
	            0x9800: 0x0,
	            0xa800: 0x21040080,
	            0xb800: 0x1000080,
	            0xc800: 0x20000080,
	            0xd800: 0x21000000,
	            0xe800: 0x1000000,
	            0xf800: 0x40080,
	            0x10000: 0x40000,
	            0x11000: 0x80,
	            0x12000: 0x20000000,
	            0x13000: 0x21000080,
	            0x14000: 0x1000080,
	            0x15000: 0x21040000,
	            0x16000: 0x20040080,
	            0x17000: 0x1000000,
	            0x18000: 0x21040080,
	            0x19000: 0x21000000,
	            0x1a000: 0x1040000,
	            0x1b000: 0x20040000,
	            0x1c000: 0x40080,
	            0x1d000: 0x20000080,
	            0x1e000: 0x0,
	            0x1f000: 0x1040080,
	            0x10800: 0x21000080,
	            0x11800: 0x1000000,
	            0x12800: 0x1040000,
	            0x13800: 0x20040080,
	            0x14800: 0x20000000,
	            0x15800: 0x1040080,
	            0x16800: 0x80,
	            0x17800: 0x21040000,
	            0x18800: 0x40080,
	            0x19800: 0x21040080,
	            0x1a800: 0x0,
	            0x1b800: 0x21000000,
	            0x1c800: 0x1000080,
	            0x1d800: 0x40000,
	            0x1e800: 0x20040000,
	            0x1f800: 0x20000080
	        },
	        {
	            0x0: 0x10000008,
	            0x100: 0x2000,
	            0x200: 0x10200000,
	            0x300: 0x10202008,
	            0x400: 0x10002000,
	            0x500: 0x200000,
	            0x600: 0x200008,
	            0x700: 0x10000000,
	            0x800: 0x0,
	            0x900: 0x10002008,
	            0xa00: 0x202000,
	            0xb00: 0x8,
	            0xc00: 0x10200008,
	            0xd00: 0x202008,
	            0xe00: 0x2008,
	            0xf00: 0x10202000,
	            0x80: 0x10200000,
	            0x180: 0x10202008,
	            0x280: 0x8,
	            0x380: 0x200000,
	            0x480: 0x202008,
	            0x580: 0x10000008,
	            0x680: 0x10002000,
	            0x780: 0x2008,
	            0x880: 0x200008,
	            0x980: 0x2000,
	            0xa80: 0x10002008,
	            0xb80: 0x10200008,
	            0xc80: 0x0,
	            0xd80: 0x10202000,
	            0xe80: 0x202000,
	            0xf80: 0x10000000,
	            0x1000: 0x10002000,
	            0x1100: 0x10200008,
	            0x1200: 0x10202008,
	            0x1300: 0x2008,
	            0x1400: 0x200000,
	            0x1500: 0x10000000,
	            0x1600: 0x10000008,
	            0x1700: 0x202000,
	            0x1800: 0x202008,
	            0x1900: 0x0,
	            0x1a00: 0x8,
	            0x1b00: 0x10200000,
	            0x1c00: 0x2000,
	            0x1d00: 0x10002008,
	            0x1e00: 0x10202000,
	            0x1f00: 0x200008,
	            0x1080: 0x8,
	            0x1180: 0x202000,
	            0x1280: 0x200000,
	            0x1380: 0x10000008,
	            0x1480: 0x10002000,
	            0x1580: 0x2008,
	            0x1680: 0x10202008,
	            0x1780: 0x10200000,
	            0x1880: 0x10202000,
	            0x1980: 0x10200008,
	            0x1a80: 0x2000,
	            0x1b80: 0x202008,
	            0x1c80: 0x200008,
	            0x1d80: 0x0,
	            0x1e80: 0x10000000,
	            0x1f80: 0x10002008
	        },
	        {
	            0x0: 0x100000,
	            0x10: 0x2000401,
	            0x20: 0x400,
	            0x30: 0x100401,
	            0x40: 0x2100401,
	            0x50: 0x0,
	            0x60: 0x1,
	            0x70: 0x2100001,
	            0x80: 0x2000400,
	            0x90: 0x100001,
	            0xa0: 0x2000001,
	            0xb0: 0x2100400,
	            0xc0: 0x2100000,
	            0xd0: 0x401,
	            0xe0: 0x100400,
	            0xf0: 0x2000000,
	            0x8: 0x2100001,
	            0x18: 0x0,
	            0x28: 0x2000401,
	            0x38: 0x2100400,
	            0x48: 0x100000,
	            0x58: 0x2000001,
	            0x68: 0x2000000,
	            0x78: 0x401,
	            0x88: 0x100401,
	            0x98: 0x2000400,
	            0xa8: 0x2100000,
	            0xb8: 0x100001,
	            0xc8: 0x400,
	            0xd8: 0x2100401,
	            0xe8: 0x1,
	            0xf8: 0x100400,
	            0x100: 0x2000000,
	            0x110: 0x100000,
	            0x120: 0x2000401,
	            0x130: 0x2100001,
	            0x140: 0x100001,
	            0x150: 0x2000400,
	            0x160: 0x2100400,
	            0x170: 0x100401,
	            0x180: 0x401,
	            0x190: 0x2100401,
	            0x1a0: 0x100400,
	            0x1b0: 0x1,
	            0x1c0: 0x0,
	            0x1d0: 0x2100000,
	            0x1e0: 0x2000001,
	            0x1f0: 0x400,
	            0x108: 0x100400,
	            0x118: 0x2000401,
	            0x128: 0x2100001,
	            0x138: 0x1,
	            0x148: 0x2000000,
	            0x158: 0x100000,
	            0x168: 0x401,
	            0x178: 0x2100400,
	            0x188: 0x2000001,
	            0x198: 0x2100000,
	            0x1a8: 0x0,
	            0x1b8: 0x2100401,
	            0x1c8: 0x100401,
	            0x1d8: 0x400,
	            0x1e8: 0x2000400,
	            0x1f8: 0x100001
	        },
	        {
	            0x0: 0x8000820,
	            0x1: 0x20000,
	            0x2: 0x8000000,
	            0x3: 0x20,
	            0x4: 0x20020,
	            0x5: 0x8020820,
	            0x6: 0x8020800,
	            0x7: 0x800,
	            0x8: 0x8020000,
	            0x9: 0x8000800,
	            0xa: 0x20800,
	            0xb: 0x8020020,
	            0xc: 0x820,
	            0xd: 0x0,
	            0xe: 0x8000020,
	            0xf: 0x20820,
	            0x80000000: 0x800,
	            0x80000001: 0x8020820,
	            0x80000002: 0x8000820,
	            0x80000003: 0x8000000,
	            0x80000004: 0x8020000,
	            0x80000005: 0x20800,
	            0x80000006: 0x20820,
	            0x80000007: 0x20,
	            0x80000008: 0x8000020,
	            0x80000009: 0x820,
	            0x8000000a: 0x20020,
	            0x8000000b: 0x8020800,
	            0x8000000c: 0x0,
	            0x8000000d: 0x8020020,
	            0x8000000e: 0x8000800,
	            0x8000000f: 0x20000,
	            0x10: 0x20820,
	            0x11: 0x8020800,
	            0x12: 0x20,
	            0x13: 0x800,
	            0x14: 0x8000800,
	            0x15: 0x8000020,
	            0x16: 0x8020020,
	            0x17: 0x20000,
	            0x18: 0x0,
	            0x19: 0x20020,
	            0x1a: 0x8020000,
	            0x1b: 0x8000820,
	            0x1c: 0x8020820,
	            0x1d: 0x20800,
	            0x1e: 0x820,
	            0x1f: 0x8000000,
	            0x80000010: 0x20000,
	            0x80000011: 0x800,
	            0x80000012: 0x8020020,
	            0x80000013: 0x20820,
	            0x80000014: 0x20,
	            0x80000015: 0x8020000,
	            0x80000016: 0x8000000,
	            0x80000017: 0x8000820,
	            0x80000018: 0x8020820,
	            0x80000019: 0x8000020,
	            0x8000001a: 0x8000800,
	            0x8000001b: 0x0,
	            0x8000001c: 0x20800,
	            0x8000001d: 0x820,
	            0x8000001e: 0x20020,
	            0x8000001f: 0x8020800
	        }
	    ];

	    // Masks that select the SBOX input
	    var SBOX_MASK = [
	        0xf8000001, 0x1f800000, 0x01f80000, 0x001f8000,
	        0x0001f800, 0x00001f80, 0x000001f8, 0x8000001f
	    ];

	    /**
	     * DES block cipher algorithm.
	     */
	    var DES = C_algo.DES = BlockCipher.extend({
	        _doReset: function () {
	            // Shortcuts
	            var key = this._key;
	            var keyWords = key.words;

	            // Select 56 bits according to PC1
	            var keyBits = [];
	            for (var i = 0; i < 56; i++) {
	                var keyBitPos = PC1[i] - 1;
	                keyBits[i] = (keyWords[keyBitPos >>> 5] >>> (31 - keyBitPos % 32)) & 1;
	            }

	            // Assemble 16 subkeys
	            var subKeys = this._subKeys = [];
	            for (var nSubKey = 0; nSubKey < 16; nSubKey++) {
	                // Create subkey
	                var subKey = subKeys[nSubKey] = [];

	                // Shortcut
	                var bitShift = BIT_SHIFTS[nSubKey];

	                // Select 48 bits according to PC2
	                for (var i = 0; i < 24; i++) {
	                    // Select from the left 28 key bits
	                    subKey[(i / 6) | 0] |= keyBits[((PC2[i] - 1) + bitShift) % 28] << (31 - i % 6);

	                    // Select from the right 28 key bits
	                    subKey[4 + ((i / 6) | 0)] |= keyBits[28 + (((PC2[i + 24] - 1) + bitShift) % 28)] << (31 - i % 6);
	                }

	                // Since each subkey is applied to an expanded 32-bit input,
	                // the subkey can be broken into 8 values scaled to 32-bits,
	                // which allows the key to be used without expansion
	                subKey[0] = (subKey[0] << 1) | (subKey[0] >>> 31);
	                for (var i = 1; i < 7; i++) {
	                    subKey[i] = subKey[i] >>> ((i - 1) * 4 + 3);
	                }
	                subKey[7] = (subKey[7] << 5) | (subKey[7] >>> 27);
	            }

	            // Compute inverse subkeys
	            var invSubKeys = this._invSubKeys = [];
	            for (var i = 0; i < 16; i++) {
	                invSubKeys[i] = subKeys[15 - i];
	            }
	        },

	        encryptBlock: function (M, offset) {
	            this._doCryptBlock(M, offset, this._subKeys);
	        },

	        decryptBlock: function (M, offset) {
	            this._doCryptBlock(M, offset, this._invSubKeys);
	        },

	        _doCryptBlock: function (M, offset, subKeys) {
	            // Get input
	            this._lBlock = M[offset];
	            this._rBlock = M[offset + 1];

	            // Initial permutation
	            exchangeLR.call(this, 4,  0x0f0f0f0f);
	            exchangeLR.call(this, 16, 0x0000ffff);
	            exchangeRL.call(this, 2,  0x33333333);
	            exchangeRL.call(this, 8,  0x00ff00ff);
	            exchangeLR.call(this, 1,  0x55555555);

	            // Rounds
	            for (var round = 0; round < 16; round++) {
	                // Shortcuts
	                var subKey = subKeys[round];
	                var lBlock = this._lBlock;
	                var rBlock = this._rBlock;

	                // Feistel function
	                var f = 0;
	                for (var i = 0; i < 8; i++) {
	                    f |= SBOX_P[i][((rBlock ^ subKey[i]) & SBOX_MASK[i]) >>> 0];
	                }
	                this._lBlock = rBlock;
	                this._rBlock = lBlock ^ f;
	            }

	            // Undo swap from last round
	            var t = this._lBlock;
	            this._lBlock = this._rBlock;
	            this._rBlock = t;

	            // Final permutation
	            exchangeLR.call(this, 1,  0x55555555);
	            exchangeRL.call(this, 8,  0x00ff00ff);
	            exchangeRL.call(this, 2,  0x33333333);
	            exchangeLR.call(this, 16, 0x0000ffff);
	            exchangeLR.call(this, 4,  0x0f0f0f0f);

	            // Set output
	            M[offset] = this._lBlock;
	            M[offset + 1] = this._rBlock;
	        },

	        keySize: 64/32,

	        ivSize: 64/32,

	        blockSize: 64/32
	    });

	    // Swap bits across the left and right words
	    function exchangeLR(offset, mask) {
	        var t = ((this._lBlock >>> offset) ^ this._rBlock) & mask;
	        this._rBlock ^= t;
	        this._lBlock ^= t << offset;
	    }

	    function exchangeRL(offset, mask) {
	        var t = ((this._rBlock >>> offset) ^ this._lBlock) & mask;
	        this._lBlock ^= t;
	        this._rBlock ^= t << offset;
	    }

	    /**
	     * Shortcut functions to the cipher's object interface.
	     *
	     * @example
	     *
	     *     var ciphertext = CryptoJS.DES.encrypt(message, key, cfg);
	     *     var plaintext  = CryptoJS.DES.decrypt(ciphertext, key, cfg);
	     */
	    C.DES = BlockCipher._createHelper(DES);

	    /**
	     * Triple-DES block cipher algorithm.
	     */
	    var TripleDES = C_algo.TripleDES = BlockCipher.extend({
	        _doReset: function () {
	            // Shortcuts
	            var key = this._key;
	            var keyWords = key.words;

	            // Create DES instances
	            this._des1 = DES.createEncryptor(WordArray.create(keyWords.slice(0, 2)));
	            this._des2 = DES.createEncryptor(WordArray.create(keyWords.slice(2, 4)));
	            this._des3 = DES.createEncryptor(WordArray.create(keyWords.slice(4, 6)));
	        },

	        encryptBlock: function (M, offset) {
	            this._des1.encryptBlock(M, offset);
	            this._des2.decryptBlock(M, offset);
	            this._des3.encryptBlock(M, offset);
	        },

	        decryptBlock: function (M, offset) {
	            this._des3.decryptBlock(M, offset);
	            this._des2.encryptBlock(M, offset);
	            this._des1.decryptBlock(M, offset);
	        },

	        keySize: 192/32,

	        ivSize: 64/32,

	        blockSize: 64/32
	    });

	    /**
	     * Shortcut functions to the cipher's object interface.
	     *
	     * @example
	     *
	     *     var ciphertext = CryptoJS.TripleDES.encrypt(message, key, cfg);
	     *     var plaintext  = CryptoJS.TripleDES.decrypt(ciphertext, key, cfg);
	     */
	    C.TripleDES = BlockCipher._createHelper(TripleDES);
	}());


	return CryptoJS.TripleDES;

}));

/***/ }),
/* 383 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(87), __webpack_require__(88), __webpack_require__(49), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var StreamCipher = C_lib.StreamCipher;
	    var C_algo = C.algo;

	    /**
	     * RC4 stream cipher algorithm.
	     */
	    var RC4 = C_algo.RC4 = StreamCipher.extend({
	        _doReset: function () {
	            // Shortcuts
	            var key = this._key;
	            var keyWords = key.words;
	            var keySigBytes = key.sigBytes;

	            // Init sbox
	            var S = this._S = [];
	            for (var i = 0; i < 256; i++) {
	                S[i] = i;
	            }

	            // Key setup
	            for (var i = 0, j = 0; i < 256; i++) {
	                var keyByteIndex = i % keySigBytes;
	                var keyByte = (keyWords[keyByteIndex >>> 2] >>> (24 - (keyByteIndex % 4) * 8)) & 0xff;

	                j = (j + S[i] + keyByte) % 256;

	                // Swap
	                var t = S[i];
	                S[i] = S[j];
	                S[j] = t;
	            }

	            // Counters
	            this._i = this._j = 0;
	        },

	        _doProcessBlock: function (M, offset) {
	            M[offset] ^= generateKeystreamWord.call(this);
	        },

	        keySize: 256/32,

	        ivSize: 0
	    });

	    function generateKeystreamWord() {
	        // Shortcuts
	        var S = this._S;
	        var i = this._i;
	        var j = this._j;

	        // Generate keystream word
	        var keystreamWord = 0;
	        for (var n = 0; n < 4; n++) {
	            i = (i + 1) % 256;
	            j = (j + S[i]) % 256;

	            // Swap
	            var t = S[i];
	            S[i] = S[j];
	            S[j] = t;

	            keystreamWord |= S[(S[i] + S[j]) % 256] << (24 - n * 8);
	        }

	        // Update counters
	        this._i = i;
	        this._j = j;

	        return keystreamWord;
	    }

	    /**
	     * Shortcut functions to the cipher's object interface.
	     *
	     * @example
	     *
	     *     var ciphertext = CryptoJS.RC4.encrypt(message, key, cfg);
	     *     var plaintext  = CryptoJS.RC4.decrypt(ciphertext, key, cfg);
	     */
	    C.RC4 = StreamCipher._createHelper(RC4);

	    /**
	     * Modified RC4 stream cipher algorithm.
	     */
	    var RC4Drop = C_algo.RC4Drop = RC4.extend({
	        /**
	         * Configuration options.
	         *
	         * @property {number} drop The number of keystream words to drop. Default 192
	         */
	        cfg: RC4.cfg.extend({
	            drop: 192
	        }),

	        _doReset: function () {
	            RC4._doReset.call(this);

	            // Drop
	            for (var i = this.cfg.drop; i > 0; i--) {
	                generateKeystreamWord.call(this);
	            }
	        }
	    });

	    /**
	     * Shortcut functions to the cipher's object interface.
	     *
	     * @example
	     *
	     *     var ciphertext = CryptoJS.RC4Drop.encrypt(message, key, cfg);
	     *     var plaintext  = CryptoJS.RC4Drop.decrypt(ciphertext, key, cfg);
	     */
	    C.RC4Drop = StreamCipher._createHelper(RC4Drop);
	}());


	return CryptoJS.RC4;

}));

/***/ }),
/* 384 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(87), __webpack_require__(88), __webpack_require__(49), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var StreamCipher = C_lib.StreamCipher;
	    var C_algo = C.algo;

	    // Reusable objects
	    var S  = [];
	    var C_ = [];
	    var G  = [];

	    /**
	     * Rabbit stream cipher algorithm
	     */
	    var Rabbit = C_algo.Rabbit = StreamCipher.extend({
	        _doReset: function () {
	            // Shortcuts
	            var K = this._key.words;
	            var iv = this.cfg.iv;

	            // Swap endian
	            for (var i = 0; i < 4; i++) {
	                K[i] = (((K[i] << 8)  | (K[i] >>> 24)) & 0x00ff00ff) |
	                       (((K[i] << 24) | (K[i] >>> 8))  & 0xff00ff00);
	            }

	            // Generate initial state values
	            var X = this._X = [
	                K[0], (K[3] << 16) | (K[2] >>> 16),
	                K[1], (K[0] << 16) | (K[3] >>> 16),
	                K[2], (K[1] << 16) | (K[0] >>> 16),
	                K[3], (K[2] << 16) | (K[1] >>> 16)
	            ];

	            // Generate initial counter values
	            var C = this._C = [
	                (K[2] << 16) | (K[2] >>> 16), (K[0] & 0xffff0000) | (K[1] & 0x0000ffff),
	                (K[3] << 16) | (K[3] >>> 16), (K[1] & 0xffff0000) | (K[2] & 0x0000ffff),
	                (K[0] << 16) | (K[0] >>> 16), (K[2] & 0xffff0000) | (K[3] & 0x0000ffff),
	                (K[1] << 16) | (K[1] >>> 16), (K[3] & 0xffff0000) | (K[0] & 0x0000ffff)
	            ];

	            // Carry bit
	            this._b = 0;

	            // Iterate the system four times
	            for (var i = 0; i < 4; i++) {
	                nextState.call(this);
	            }

	            // Modify the counters
	            for (var i = 0; i < 8; i++) {
	                C[i] ^= X[(i + 4) & 7];
	            }

	            // IV setup
	            if (iv) {
	                // Shortcuts
	                var IV = iv.words;
	                var IV_0 = IV[0];
	                var IV_1 = IV[1];

	                // Generate four subvectors
	                var i0 = (((IV_0 << 8) | (IV_0 >>> 24)) & 0x00ff00ff) | (((IV_0 << 24) | (IV_0 >>> 8)) & 0xff00ff00);
	                var i2 = (((IV_1 << 8) | (IV_1 >>> 24)) & 0x00ff00ff) | (((IV_1 << 24) | (IV_1 >>> 8)) & 0xff00ff00);
	                var i1 = (i0 >>> 16) | (i2 & 0xffff0000);
	                var i3 = (i2 << 16)  | (i0 & 0x0000ffff);

	                // Modify counter values
	                C[0] ^= i0;
	                C[1] ^= i1;
	                C[2] ^= i2;
	                C[3] ^= i3;
	                C[4] ^= i0;
	                C[5] ^= i1;
	                C[6] ^= i2;
	                C[7] ^= i3;

	                // Iterate the system four times
	                for (var i = 0; i < 4; i++) {
	                    nextState.call(this);
	                }
	            }
	        },

	        _doProcessBlock: function (M, offset) {
	            // Shortcut
	            var X = this._X;

	            // Iterate the system
	            nextState.call(this);

	            // Generate four keystream words
	            S[0] = X[0] ^ (X[5] >>> 16) ^ (X[3] << 16);
	            S[1] = X[2] ^ (X[7] >>> 16) ^ (X[5] << 16);
	            S[2] = X[4] ^ (X[1] >>> 16) ^ (X[7] << 16);
	            S[3] = X[6] ^ (X[3] >>> 16) ^ (X[1] << 16);

	            for (var i = 0; i < 4; i++) {
	                // Swap endian
	                S[i] = (((S[i] << 8)  | (S[i] >>> 24)) & 0x00ff00ff) |
	                       (((S[i] << 24) | (S[i] >>> 8))  & 0xff00ff00);

	                // Encrypt
	                M[offset + i] ^= S[i];
	            }
	        },

	        blockSize: 128/32,

	        ivSize: 64/32
	    });

	    function nextState() {
	        // Shortcuts
	        var X = this._X;
	        var C = this._C;

	        // Save old counter values
	        for (var i = 0; i < 8; i++) {
	            C_[i] = C[i];
	        }

	        // Calculate new counter values
	        C[0] = (C[0] + 0x4d34d34d + this._b) | 0;
	        C[1] = (C[1] + 0xd34d34d3 + ((C[0] >>> 0) < (C_[0] >>> 0) ? 1 : 0)) | 0;
	        C[2] = (C[2] + 0x34d34d34 + ((C[1] >>> 0) < (C_[1] >>> 0) ? 1 : 0)) | 0;
	        C[3] = (C[3] + 0x4d34d34d + ((C[2] >>> 0) < (C_[2] >>> 0) ? 1 : 0)) | 0;
	        C[4] = (C[4] + 0xd34d34d3 + ((C[3] >>> 0) < (C_[3] >>> 0) ? 1 : 0)) | 0;
	        C[5] = (C[5] + 0x34d34d34 + ((C[4] >>> 0) < (C_[4] >>> 0) ? 1 : 0)) | 0;
	        C[6] = (C[6] + 0x4d34d34d + ((C[5] >>> 0) < (C_[5] >>> 0) ? 1 : 0)) | 0;
	        C[7] = (C[7] + 0xd34d34d3 + ((C[6] >>> 0) < (C_[6] >>> 0) ? 1 : 0)) | 0;
	        this._b = (C[7] >>> 0) < (C_[7] >>> 0) ? 1 : 0;

	        // Calculate the g-values
	        for (var i = 0; i < 8; i++) {
	            var gx = X[i] + C[i];

	            // Construct high and low argument for squaring
	            var ga = gx & 0xffff;
	            var gb = gx >>> 16;

	            // Calculate high and low result of squaring
	            var gh = ((((ga * ga) >>> 17) + ga * gb) >>> 15) + gb * gb;
	            var gl = (((gx & 0xffff0000) * gx) | 0) + (((gx & 0x0000ffff) * gx) | 0);

	            // High XOR low
	            G[i] = gh ^ gl;
	        }

	        // Calculate new state values
	        X[0] = (G[0] + ((G[7] << 16) | (G[7] >>> 16)) + ((G[6] << 16) | (G[6] >>> 16))) | 0;
	        X[1] = (G[1] + ((G[0] << 8)  | (G[0] >>> 24)) + G[7]) | 0;
	        X[2] = (G[2] + ((G[1] << 16) | (G[1] >>> 16)) + ((G[0] << 16) | (G[0] >>> 16))) | 0;
	        X[3] = (G[3] + ((G[2] << 8)  | (G[2] >>> 24)) + G[1]) | 0;
	        X[4] = (G[4] + ((G[3] << 16) | (G[3] >>> 16)) + ((G[2] << 16) | (G[2] >>> 16))) | 0;
	        X[5] = (G[5] + ((G[4] << 8)  | (G[4] >>> 24)) + G[3]) | 0;
	        X[6] = (G[6] + ((G[5] << 16) | (G[5] >>> 16)) + ((G[4] << 16) | (G[4] >>> 16))) | 0;
	        X[7] = (G[7] + ((G[6] << 8)  | (G[6] >>> 24)) + G[5]) | 0;
	    }

	    /**
	     * Shortcut functions to the cipher's object interface.
	     *
	     * @example
	     *
	     *     var ciphertext = CryptoJS.Rabbit.encrypt(message, key, cfg);
	     *     var plaintext  = CryptoJS.Rabbit.decrypt(ciphertext, key, cfg);
	     */
	    C.Rabbit = StreamCipher._createHelper(Rabbit);
	}());


	return CryptoJS.Rabbit;

}));

/***/ }),
/* 385 */
/***/ (function(module, exports, __webpack_require__) {

;(function (root, factory, undef) {
	if (true) {
		// CommonJS
		module.exports = exports = factory(__webpack_require__(2), __webpack_require__(87), __webpack_require__(88), __webpack_require__(49), __webpack_require__(12));
	}
	else {}
}(this, function (CryptoJS) {

	(function () {
	    // Shortcuts
	    var C = CryptoJS;
	    var C_lib = C.lib;
	    var StreamCipher = C_lib.StreamCipher;
	    var C_algo = C.algo;

	    // Reusable objects
	    var S  = [];
	    var C_ = [];
	    var G  = [];

	    /**
	     * Rabbit stream cipher algorithm.
	     *
	     * This is a legacy version that neglected to convert the key to little-endian.
	     * This error doesn't affect the cipher's security,
	     * but it does affect its compatibility with other implementations.
	     */
	    var RabbitLegacy = C_algo.RabbitLegacy = StreamCipher.extend({
	        _doReset: function () {
	            // Shortcuts
	            var K = this._key.words;
	            var iv = this.cfg.iv;

	            // Generate initial state values
	            var X = this._X = [
	                K[0], (K[3] << 16) | (K[2] >>> 16),
	                K[1], (K[0] << 16) | (K[3] >>> 16),
	                K[2], (K[1] << 16) | (K[0] >>> 16),
	                K[3], (K[2] << 16) | (K[1] >>> 16)
	            ];

	            // Generate initial counter values
	            var C = this._C = [
	                (K[2] << 16) | (K[2] >>> 16), (K[0] & 0xffff0000) | (K[1] & 0x0000ffff),
	                (K[3] << 16) | (K[3] >>> 16), (K[1] & 0xffff0000) | (K[2] & 0x0000ffff),
	                (K[0] << 16) | (K[0] >>> 16), (K[2] & 0xffff0000) | (K[3] & 0x0000ffff),
	                (K[1] << 16) | (K[1] >>> 16), (K[3] & 0xffff0000) | (K[0] & 0x0000ffff)
	            ];

	            // Carry bit
	            this._b = 0;

	            // Iterate the system four times
	            for (var i = 0; i < 4; i++) {
	                nextState.call(this);
	            }

	            // Modify the counters
	            for (var i = 0; i < 8; i++) {
	                C[i] ^= X[(i + 4) & 7];
	            }

	            // IV setup
	            if (iv) {
	                // Shortcuts
	                var IV = iv.words;
	                var IV_0 = IV[0];
	                var IV_1 = IV[1];

	                // Generate four subvectors
	                var i0 = (((IV_0 << 8) | (IV_0 >>> 24)) & 0x00ff00ff) | (((IV_0 << 24) | (IV_0 >>> 8)) & 0xff00ff00);
	                var i2 = (((IV_1 << 8) | (IV_1 >>> 24)) & 0x00ff00ff) | (((IV_1 << 24) | (IV_1 >>> 8)) & 0xff00ff00);
	                var i1 = (i0 >>> 16) | (i2 & 0xffff0000);
	                var i3 = (i2 << 16)  | (i0 & 0x0000ffff);

	                // Modify counter values
	                C[0] ^= i0;
	                C[1] ^= i1;
	                C[2] ^= i2;
	                C[3] ^= i3;
	                C[4] ^= i0;
	                C[5] ^= i1;
	                C[6] ^= i2;
	                C[7] ^= i3;

	                // Iterate the system four times
	                for (var i = 0; i < 4; i++) {
	                    nextState.call(this);
	                }
	            }
	        },

	        _doProcessBlock: function (M, offset) {
	            // Shortcut
	            var X = this._X;

	            // Iterate the system
	            nextState.call(this);

	            // Generate four keystream words
	            S[0] = X[0] ^ (X[5] >>> 16) ^ (X[3] << 16);
	            S[1] = X[2] ^ (X[7] >>> 16) ^ (X[5] << 16);
	            S[2] = X[4] ^ (X[1] >>> 16) ^ (X[7] << 16);
	            S[3] = X[6] ^ (X[3] >>> 16) ^ (X[1] << 16);

	            for (var i = 0; i < 4; i++) {
	                // Swap endian
	                S[i] = (((S[i] << 8)  | (S[i] >>> 24)) & 0x00ff00ff) |
	                       (((S[i] << 24) | (S[i] >>> 8))  & 0xff00ff00);

	                // Encrypt
	                M[offset + i] ^= S[i];
	            }
	        },

	        blockSize: 128/32,

	        ivSize: 64/32
	    });

	    function nextState() {
	        // Shortcuts
	        var X = this._X;
	        var C = this._C;

	        // Save old counter values
	        for (var i = 0; i < 8; i++) {
	            C_[i] = C[i];
	        }

	        // Calculate new counter values
	        C[0] = (C[0] + 0x4d34d34d + this._b) | 0;
	        C[1] = (C[1] + 0xd34d34d3 + ((C[0] >>> 0) < (C_[0] >>> 0) ? 1 : 0)) | 0;
	        C[2] = (C[2] + 0x34d34d34 + ((C[1] >>> 0) < (C_[1] >>> 0) ? 1 : 0)) | 0;
	        C[3] = (C[3] + 0x4d34d34d + ((C[2] >>> 0) < (C_[2] >>> 0) ? 1 : 0)) | 0;
	        C[4] = (C[4] + 0xd34d34d3 + ((C[3] >>> 0) < (C_[3] >>> 0) ? 1 : 0)) | 0;
	        C[5] = (C[5] + 0x34d34d34 + ((C[4] >>> 0) < (C_[4] >>> 0) ? 1 : 0)) | 0;
	        C[6] = (C[6] + 0x4d34d34d + ((C[5] >>> 0) < (C_[5] >>> 0) ? 1 : 0)) | 0;
	        C[7] = (C[7] + 0xd34d34d3 + ((C[6] >>> 0) < (C_[6] >>> 0) ? 1 : 0)) | 0;
	        this._b = (C[7] >>> 0) < (C_[7] >>> 0) ? 1 : 0;

	        // Calculate the g-values
	        for (var i = 0; i < 8; i++) {
	            var gx = X[i] + C[i];

	            // Construct high and low argument for squaring
	            var ga = gx & 0xffff;
	            var gb = gx >>> 16;

	            // Calculate high and low result of squaring
	            var gh = ((((ga * ga) >>> 17) + ga * gb) >>> 15) + gb * gb;
	            var gl = (((gx & 0xffff0000) * gx) | 0) + (((gx & 0x0000ffff) * gx) | 0);

	            // High XOR low
	            G[i] = gh ^ gl;
	        }

	        // Calculate new state values
	        X[0] = (G[0] + ((G[7] << 16) | (G[7] >>> 16)) + ((G[6] << 16) | (G[6] >>> 16))) | 0;
	        X[1] = (G[1] + ((G[0] << 8)  | (G[0] >>> 24)) + G[7]) | 0;
	        X[2] = (G[2] + ((G[1] << 16) | (G[1] >>> 16)) + ((G[0] << 16) | (G[0] >>> 16))) | 0;
	        X[3] = (G[3] + ((G[2] << 8)  | (G[2] >>> 24)) + G[1]) | 0;
	        X[4] = (G[4] + ((G[3] << 16) | (G[3] >>> 16)) + ((G[2] << 16) | (G[2] >>> 16))) | 0;
	        X[5] = (G[5] + ((G[4] << 8)  | (G[4] >>> 24)) + G[3]) | 0;
	        X[6] = (G[6] + ((G[5] << 16) | (G[5] >>> 16)) + ((G[4] << 16) | (G[4] >>> 16))) | 0;
	        X[7] = (G[7] + ((G[6] << 8)  | (G[6] >>> 24)) + G[5]) | 0;
	    }

	    /**
	     * Shortcut functions to the cipher's object interface.
	     *
	     * @example
	     *
	     *     var ciphertext = CryptoJS.RabbitLegacy.encrypt(message, key, cfg);
	     *     var plaintext  = CryptoJS.RabbitLegacy.decrypt(ciphertext, key, cfg);
	     */
	    C.RabbitLegacy = StreamCipher._createHelper(RabbitLegacy);
	}());


	return CryptoJS.RabbitLegacy;

}));

/***/ }),
/* 386 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(Buffer) {

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var r = _interopDefault(__webpack_require__(387));
var _Object$getOwnPropertyDescriptor = _interopDefault(__webpack_require__(417));
var _getIterator = _interopDefault(__webpack_require__(420));
var _Object$freeze = _interopDefault(__webpack_require__(429));
var _typeof = _interopDefault(__webpack_require__(185));
var _Object$keys = _interopDefault(__webpack_require__(441));
var _Object$defineProperty = _interopDefault(__webpack_require__(263));
var _classCallCheck = _interopDefault(__webpack_require__(446));
var _createClass = _interopDefault(__webpack_require__(447));
var _Map = _interopDefault(__webpack_require__(448));
var _possibleConstructorReturn = _interopDefault(__webpack_require__(458));
var _inherits = _interopDefault(__webpack_require__(459));
var restructure_src_utils = __webpack_require__(40);
var _Object$defineProperties = _interopDefault(__webpack_require__(467));
var isEqual = _interopDefault(__webpack_require__(470));
var _Object$assign = _interopDefault(__webpack_require__(482));
var _String$fromCodePoint = _interopDefault(__webpack_require__(486));
var _Array$from = _interopDefault(__webpack_require__(489));
var _Set = _interopDefault(__webpack_require__(493));
var unicode = _interopDefault(__webpack_require__(499));
var UnicodeTrie = _interopDefault(__webpack_require__(504));
var StateMachine = _interopDefault(__webpack_require__(505));
var _Number$EPSILON = _interopDefault(__webpack_require__(507));
var cloneDeep = _interopDefault(__webpack_require__(510));
var _Promise = _interopDefault(__webpack_require__(511));
var inflate = _interopDefault(__webpack_require__(135));
var brotli = _interopDefault(__webpack_require__(519));

var fs = __webpack_require__(53);

var fontkit = {};
fontkit.logErrors = false;

var formats = [];
fontkit.registerFormat = function (format) {
  formats.push(format);
};

fontkit.openSync = function (filename, postscriptName) {
  var buffer = fs.readFileSync(filename);
  return fontkit.create(buffer, postscriptName);
};

fontkit.open = function (filename, postscriptName, callback) {
  if (typeof postscriptName === 'function') {
    callback = postscriptName;
    postscriptName = null;
  }

  fs.readFile(filename, function (err, buffer) {
    if (err) {
      return callback(err);
    }

    try {
      var font = fontkit.create(buffer, postscriptName);
    } catch (e) {
      return callback(e);
    }

    return callback(null, font);
  });

  return;
};

fontkit.create = function (buffer, postscriptName) {
  for (var i = 0; i < formats.length; i++) {
    var format = formats[i];
    if (format.probe(buffer)) {
      var font = new format(new r.DecodeStream(buffer));
      if (postscriptName) {
        return font.getFont(postscriptName);
      }

      return font;
    }
  }

  throw new Error('Unknown font format');
};

fontkit.defaultLanguage = 'en';
fontkit.setDefaultLanguage = function () {
  var lang = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'en';

  fontkit.defaultLanguage = lang;
};

/**
 * This decorator caches the results of a getter or method such that
 * the results are lazily computed once, and then cached.
 * @private
 */
function cache(target, key, descriptor) {
  if (descriptor.get) {
    var get = descriptor.get;
    descriptor.get = function () {
      var value = get.call(this);
      _Object$defineProperty(this, key, { value: value });
      return value;
    };
  } else if (typeof descriptor.value === 'function') {
    var fn = descriptor.value;

    return {
      get: function get() {
        var cache = new _Map();
        function memoized() {
          for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
            args[_key] = arguments[_key];
          }

          var key = args.length > 0 ? args[0] : 'value';
          if (cache.has(key)) {
            return cache.get(key);
          }

          var result = fn.apply(this, args);
          cache.set(key, result);
          return result;
        };

        _Object$defineProperty(this, key, { value: memoized });
        return memoized;
      }
    };
  }
}

var SubHeader = new r.Struct({
  firstCode: r.uint16,
  entryCount: r.uint16,
  idDelta: r.int16,
  idRangeOffset: r.uint16
});

var CmapGroup = new r.Struct({
  startCharCode: r.uint32,
  endCharCode: r.uint32,
  glyphID: r.uint32
});

var UnicodeValueRange = new r.Struct({
  startUnicodeValue: r.uint24,
  additionalCount: r.uint8
});

var UVSMapping = new r.Struct({
  unicodeValue: r.uint24,
  glyphID: r.uint16
});

var DefaultUVS = new r.Array(UnicodeValueRange, r.uint32);
var NonDefaultUVS = new r.Array(UVSMapping, r.uint32);

var VarSelectorRecord = new r.Struct({
  varSelector: r.uint24,
  defaultUVS: new r.Pointer(r.uint32, DefaultUVS, { type: 'parent' }),
  nonDefaultUVS: new r.Pointer(r.uint32, NonDefaultUVS, { type: 'parent' })
});

var CmapSubtable = new r.VersionedStruct(r.uint16, {
  0: { // Byte encoding
    length: r.uint16, // Total table length in bytes (set to 262 for format 0)
    language: r.uint16, // Language code for this encoding subtable, or zero if language-independent
    codeMap: new r.LazyArray(r.uint8, 256)
  },

  2: { // High-byte mapping (CJK)
    length: r.uint16,
    language: r.uint16,
    subHeaderKeys: new r.Array(r.uint16, 256),
    subHeaderCount: function subHeaderCount(t) {
      return Math.max.apply(Math, t.subHeaderKeys);
    },
    subHeaders: new r.LazyArray(SubHeader, 'subHeaderCount'),
    glyphIndexArray: new r.LazyArray(r.uint16, 'subHeaderCount')
  },

  4: { // Segment mapping to delta values
    length: r.uint16, // Total table length in bytes
    language: r.uint16, // Language code
    segCountX2: r.uint16,
    segCount: function segCount(t) {
      return t.segCountX2 >> 1;
    },
    searchRange: r.uint16,
    entrySelector: r.uint16,
    rangeShift: r.uint16,
    endCode: new r.LazyArray(r.uint16, 'segCount'),
    reservedPad: new r.Reserved(r.uint16), // This value should be zero
    startCode: new r.LazyArray(r.uint16, 'segCount'),
    idDelta: new r.LazyArray(r.int16, 'segCount'),
    idRangeOffset: new r.LazyArray(r.uint16, 'segCount'),
    glyphIndexArray: new r.LazyArray(r.uint16, function (t) {
      return (t.length - t._currentOffset) / 2;
    })
  },

  6: { // Trimmed table
    length: r.uint16,
    language: r.uint16,
    firstCode: r.uint16,
    entryCount: r.uint16,
    glyphIndices: new r.LazyArray(r.uint16, 'entryCount')
  },

  8: { // mixed 16-bit and 32-bit coverage
    reserved: new r.Reserved(r.uint16),
    length: r.uint32,
    language: r.uint16,
    is32: new r.LazyArray(r.uint8, 8192),
    nGroups: r.uint32,
    groups: new r.LazyArray(CmapGroup, 'nGroups')
  },

  10: { // Trimmed Array
    reserved: new r.Reserved(r.uint16),
    length: r.uint32,
    language: r.uint32,
    firstCode: r.uint32,
    entryCount: r.uint32,
    glyphIndices: new r.LazyArray(r.uint16, 'numChars')
  },

  12: { // Segmented coverage
    reserved: new r.Reserved(r.uint16),
    length: r.uint32,
    language: r.uint32,
    nGroups: r.uint32,
    groups: new r.LazyArray(CmapGroup, 'nGroups')
  },

  13: { // Many-to-one range mappings (same as 12 except for group.startGlyphID)
    reserved: new r.Reserved(r.uint16),
    length: r.uint32,
    language: r.uint32,
    nGroups: r.uint32,
    groups: new r.LazyArray(CmapGroup, 'nGroups')
  },

  14: { // Unicode Variation Sequences
    length: r.uint32,
    numRecords: r.uint32,
    varSelectors: new r.LazyArray(VarSelectorRecord, 'numRecords')
  }
});

var CmapEntry = new r.Struct({
  platformID: r.uint16, // Platform identifier
  encodingID: r.uint16, // Platform-specific encoding identifier
  table: new r.Pointer(r.uint32, CmapSubtable, { type: 'parent', lazy: true })
});

// character to glyph mapping
var cmap = new r.Struct({
  version: r.uint16,
  numSubtables: r.uint16,
  tables: new r.Array(CmapEntry, 'numSubtables')
});

// font header
var head = new r.Struct({
  version: r.int32, // 0x00010000 (version 1.0)
  revision: r.int32, // set by font manufacturer
  checkSumAdjustment: r.uint32,
  magicNumber: r.uint32, // set to 0x5F0F3CF5
  flags: r.uint16,
  unitsPerEm: r.uint16, // range from 64 to 16384
  created: new r.Array(r.int32, 2),
  modified: new r.Array(r.int32, 2),
  xMin: r.int16, // for all glyph bounding boxes
  yMin: r.int16, // for all glyph bounding boxes
  xMax: r.int16, // for all glyph bounding boxes
  yMax: r.int16, // for all glyph bounding boxes
  macStyle: new r.Bitfield(r.uint16, ['bold', 'italic', 'underline', 'outline', 'shadow', 'condensed', 'extended']),
  lowestRecPPEM: r.uint16, // smallest readable size in pixels
  fontDirectionHint: r.int16,
  indexToLocFormat: r.int16, // 0 for short offsets, 1 for long
  glyphDataFormat: r.int16 // 0 for current format
});

// horizontal header
var hhea = new r.Struct({
  version: r.int32,
  ascent: r.int16, // Distance from baseline of highest ascender
  descent: r.int16, // Distance from baseline of lowest descender
  lineGap: r.int16, // Typographic line gap
  advanceWidthMax: r.uint16, // Maximum advance width value in 'hmtx' table
  minLeftSideBearing: r.int16, // Maximum advance width value in 'hmtx' table
  minRightSideBearing: r.int16, // Minimum right sidebearing value
  xMaxExtent: r.int16,
  caretSlopeRise: r.int16, // Used to calculate the slope of the cursor (rise/run); 1 for vertical
  caretSlopeRun: r.int16, // 0 for vertical
  caretOffset: r.int16, // Set to 0 for non-slanted fonts
  reserved: new r.Reserved(r.int16, 4),
  metricDataFormat: r.int16, // 0 for current format
  numberOfMetrics: r.uint16 // Number of advance widths in 'hmtx' table
});

var HmtxEntry = new r.Struct({
  advance: r.uint16,
  bearing: r.int16
});

var hmtx = new r.Struct({
  metrics: new r.LazyArray(HmtxEntry, function (t) {
    return t.parent.hhea.numberOfMetrics;
  }),
  bearings: new r.LazyArray(r.int16, function (t) {
    return t.parent.maxp.numGlyphs - t.parent.hhea.numberOfMetrics;
  })
});

// maxiumum profile
var maxp = new r.Struct({
  version: r.int32,
  numGlyphs: r.uint16, // The number of glyphs in the font
  maxPoints: r.uint16, // Maximum points in a non-composite glyph
  maxContours: r.uint16, // Maximum contours in a non-composite glyph
  maxComponentPoints: r.uint16, // Maximum points in a composite glyph
  maxComponentContours: r.uint16, // Maximum contours in a composite glyph
  maxZones: r.uint16, // 1 if instructions do not use the twilight zone, 2 otherwise
  maxTwilightPoints: r.uint16, // Maximum points used in Z0
  maxStorage: r.uint16, // Number of Storage Area locations
  maxFunctionDefs: r.uint16, // Number of FDEFs
  maxInstructionDefs: r.uint16, // Number of IDEFs
  maxStackElements: r.uint16, // Maximum stack depth
  maxSizeOfInstructions: r.uint16, // Maximum byte count for glyph instructions
  maxComponentElements: r.uint16, // Maximum number of components referenced at “top level” for any composite glyph
  maxComponentDepth: r.uint16 // Maximum levels of recursion; 1 for simple components
});

/**
 * Gets an encoding name from platform, encoding, and language ids.
 * Returned encoding names can be used in iconv-lite to decode text.
 */
function getEncoding(platformID, encodingID) {
  var languageID = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;

  if (platformID === 1 && MAC_LANGUAGE_ENCODINGS[languageID]) {
    return MAC_LANGUAGE_ENCODINGS[languageID];
  }

  return ENCODINGS[platformID][encodingID];
}

// Map of platform ids to encoding ids.
var ENCODINGS = [
// unicode
['utf16be', 'utf16be', 'utf16be', 'utf16be', 'utf16be', 'utf16be'],

// macintosh
// Mappings available at http://unicode.org/Public/MAPPINGS/VENDORS/APPLE/
// 0	Roman                 17	Malayalam
// 1	Japanese	            18	Sinhalese
// 2	Traditional Chinese	  19	Burmese
// 3	Korean	              20	Khmer
// 4	Arabic	              21	Thai
// 5	Hebrew	              22	Laotian
// 6	Greek	                23	Georgian
// 7	Russian	              24	Armenian
// 8	RSymbol	              25	Simplified Chinese
// 9	Devanagari	          26	Tibetan
// 10	Gurmukhi	            27	Mongolian
// 11	Gujarati	            28	Geez
// 12	Oriya	                29	Slavic
// 13	Bengali	              30	Vietnamese
// 14	Tamil	                31	Sindhi
// 15	Telugu	              32	(Uninterpreted)
// 16	Kannada
['macroman', 'shift-jis', 'big5', 'euc-kr', 'iso-8859-6', 'iso-8859-8', 'macgreek', 'maccyrillic', 'symbol', 'Devanagari', 'Gurmukhi', 'Gujarati', 'Oriya', 'Bengali', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Sinhalese', 'Burmese', 'Khmer', 'macthai', 'Laotian', 'Georgian', 'Armenian', 'gb-2312-80', 'Tibetan', 'Mongolian', 'Geez', 'maccenteuro', 'Vietnamese', 'Sindhi'],

// ISO (deprecated)
['ascii'],

// windows
// Docs here: http://msdn.microsoft.com/en-us/library/system.text.encoding(v=vs.110).aspx
['symbol', 'utf16be', 'shift-jis', 'gb18030', 'big5', 'wansung', 'johab', null, null, null, 'utf16be']];

// Overrides for Mac scripts by language id.
// See http://unicode.org/Public/MAPPINGS/VENDORS/APPLE/Readme.txt
var MAC_LANGUAGE_ENCODINGS = {
  15: 'maciceland',
  17: 'macturkish',
  18: 'maccroatian',
  24: 'maccenteuro',
  25: 'maccenteuro',
  26: 'maccenteuro',
  27: 'maccenteuro',
  28: 'maccenteuro',
  30: 'maciceland',
  37: 'macromania',
  38: 'maccenteuro',
  39: 'maccenteuro',
  40: 'maccenteuro',
  143: 'macinuit', // Unsupported by iconv-lite
  146: 'macgaelic' // Unsupported by iconv-lite
};

// Map of platform ids to BCP-47 language codes.
var LANGUAGES = [
// unicode
[], { // macintosh
  0: 'en', 30: 'fo', 60: 'ks', 90: 'rw',
  1: 'fr', 31: 'fa', 61: 'ku', 91: 'rn',
  2: 'de', 32: 'ru', 62: 'sd', 92: 'ny',
  3: 'it', 33: 'zh', 63: 'bo', 93: 'mg',
  4: 'nl', 34: 'nl-BE', 64: 'ne', 94: 'eo',
  5: 'sv', 35: 'ga', 65: 'sa', 128: 'cy',
  6: 'es', 36: 'sq', 66: 'mr', 129: 'eu',
  7: 'da', 37: 'ro', 67: 'bn', 130: 'ca',
  8: 'pt', 38: 'cz', 68: 'as', 131: 'la',
  9: 'no', 39: 'sk', 69: 'gu', 132: 'qu',
  10: 'he', 40: 'si', 70: 'pa', 133: 'gn',
  11: 'ja', 41: 'yi', 71: 'or', 134: 'ay',
  12: 'ar', 42: 'sr', 72: 'ml', 135: 'tt',
  13: 'fi', 43: 'mk', 73: 'kn', 136: 'ug',
  14: 'el', 44: 'bg', 74: 'ta', 137: 'dz',
  15: 'is', 45: 'uk', 75: 'te', 138: 'jv',
  16: 'mt', 46: 'be', 76: 'si', 139: 'su',
  17: 'tr', 47: 'uz', 77: 'my', 140: 'gl',
  18: 'hr', 48: 'kk', 78: 'km', 141: 'af',
  19: 'zh-Hant', 49: 'az-Cyrl', 79: 'lo', 142: 'br',
  20: 'ur', 50: 'az-Arab', 80: 'vi', 143: 'iu',
  21: 'hi', 51: 'hy', 81: 'id', 144: 'gd',
  22: 'th', 52: 'ka', 82: 'tl', 145: 'gv',
  23: 'ko', 53: 'mo', 83: 'ms', 146: 'ga',
  24: 'lt', 54: 'ky', 84: 'ms-Arab', 147: 'to',
  25: 'pl', 55: 'tg', 85: 'am', 148: 'el-polyton',
  26: 'hu', 56: 'tk', 86: 'ti', 149: 'kl',
  27: 'es', 57: 'mn-CN', 87: 'om', 150: 'az',
  28: 'lv', 58: 'mn', 88: 'so', 151: 'nn',
  29: 'se', 59: 'ps', 89: 'sw'
},

// ISO (deprecated)
[], { // windows                                        
  0x0436: 'af', 0x4009: 'en-IN', 0x0487: 'rw', 0x0432: 'tn',
  0x041C: 'sq', 0x1809: 'en-IE', 0x0441: 'sw', 0x045B: 'si',
  0x0484: 'gsw', 0x2009: 'en-JM', 0x0457: 'kok', 0x041B: 'sk',
  0x045E: 'am', 0x4409: 'en-MY', 0x0412: 'ko', 0x0424: 'sl',
  0x1401: 'ar-DZ', 0x1409: 'en-NZ', 0x0440: 'ky', 0x2C0A: 'es-AR',
  0x3C01: 'ar-BH', 0x3409: 'en-PH', 0x0454: 'lo', 0x400A: 'es-BO',
  0x0C01: 'ar', 0x4809: 'en-SG', 0x0426: 'lv', 0x340A: 'es-CL',
  0x0801: 'ar-IQ', 0x1C09: 'en-ZA', 0x0427: 'lt', 0x240A: 'es-CO',
  0x2C01: 'ar-JO', 0x2C09: 'en-TT', 0x082E: 'dsb', 0x140A: 'es-CR',
  0x3401: 'ar-KW', 0x0809: 'en-GB', 0x046E: 'lb', 0x1C0A: 'es-DO',
  0x3001: 'ar-LB', 0x0409: 'en', 0x042F: 'mk', 0x300A: 'es-EC',
  0x1001: 'ar-LY', 0x3009: 'en-ZW', 0x083E: 'ms-BN', 0x440A: 'es-SV',
  0x1801: 'ary', 0x0425: 'et', 0x043E: 'ms', 0x100A: 'es-GT',
  0x2001: 'ar-OM', 0x0438: 'fo', 0x044C: 'ml', 0x480A: 'es-HN',
  0x4001: 'ar-QA', 0x0464: 'fil', 0x043A: 'mt', 0x080A: 'es-MX',
  0x0401: 'ar-SA', 0x040B: 'fi', 0x0481: 'mi', 0x4C0A: 'es-NI',
  0x2801: 'ar-SY', 0x080C: 'fr-BE', 0x047A: 'arn', 0x180A: 'es-PA',
  0x1C01: 'aeb', 0x0C0C: 'fr-CA', 0x044E: 'mr', 0x3C0A: 'es-PY',
  0x3801: 'ar-AE', 0x040C: 'fr', 0x047C: 'moh', 0x280A: 'es-PE',
  0x2401: 'ar-YE', 0x140C: 'fr-LU', 0x0450: 'mn', 0x500A: 'es-PR',
  0x042B: 'hy', 0x180C: 'fr-MC', 0x0850: 'mn-CN', 0x0C0A: 'es',
  0x044D: 'as', 0x100C: 'fr-CH', 0x0461: 'ne', 0x040A: 'es',
  0x082C: 'az-Cyrl', 0x0462: 'fy', 0x0414: 'nb', 0x540A: 'es-US',
  0x042C: 'az', 0x0456: 'gl', 0x0814: 'nn', 0x380A: 'es-UY',
  0x046D: 'ba', 0x0437: 'ka', 0x0482: 'oc', 0x200A: 'es-VE',
  0x042D: 'eu', 0x0C07: 'de-AT', 0x0448: 'or', 0x081D: 'sv-FI',
  0x0423: 'be', 0x0407: 'de', 0x0463: 'ps', 0x041D: 'sv',
  0x0845: 'bn', 0x1407: 'de-LI', 0x0415: 'pl', 0x045A: 'syr',
  0x0445: 'bn-IN', 0x1007: 'de-LU', 0x0416: 'pt', 0x0428: 'tg',
  0x201A: 'bs-Cyrl', 0x0807: 'de-CH', 0x0816: 'pt-PT', 0x085F: 'tzm',
  0x141A: 'bs', 0x0408: 'el', 0x0446: 'pa', 0x0449: 'ta',
  0x047E: 'br', 0x046F: 'kl', 0x046B: 'qu-BO', 0x0444: 'tt',
  0x0402: 'bg', 0x0447: 'gu', 0x086B: 'qu-EC', 0x044A: 'te',
  0x0403: 'ca', 0x0468: 'ha', 0x0C6B: 'qu', 0x041E: 'th',
  0x0C04: 'zh-HK', 0x040D: 'he', 0x0418: 'ro', 0x0451: 'bo',
  0x1404: 'zh-MO', 0x0439: 'hi', 0x0417: 'rm', 0x041F: 'tr',
  0x0804: 'zh', 0x040E: 'hu', 0x0419: 'ru', 0x0442: 'tk',
  0x1004: 'zh-SG', 0x040F: 'is', 0x243B: 'smn', 0x0480: 'ug',
  0x0404: 'zh-TW', 0x0470: 'ig', 0x103B: 'smj-NO', 0x0422: 'uk',
  0x0483: 'co', 0x0421: 'id', 0x143B: 'smj', 0x042E: 'hsb',
  0x041A: 'hr', 0x045D: 'iu', 0x0C3B: 'se-FI', 0x0420: 'ur',
  0x101A: 'hr-BA', 0x085D: 'iu-Latn', 0x043B: 'se', 0x0843: 'uz-Cyrl',
  0x0405: 'cs', 0x083C: 'ga', 0x083B: 'se-SE', 0x0443: 'uz',
  0x0406: 'da', 0x0434: 'xh', 0x203B: 'sms', 0x042A: 'vi',
  0x048C: 'prs', 0x0435: 'zu', 0x183B: 'sma-NO', 0x0452: 'cy',
  0x0465: 'dv', 0x0410: 'it', 0x1C3B: 'sms', 0x0488: 'wo',
  0x0813: 'nl-BE', 0x0810: 'it-CH', 0x044F: 'sa', 0x0485: 'sah',
  0x0413: 'nl', 0x0411: 'ja', 0x1C1A: 'sr-Cyrl-BA', 0x0478: 'ii',
  0x0C09: 'en-AU', 0x044B: 'kn', 0x0C1A: 'sr', 0x046A: 'yo',
  0x2809: 'en-BZ', 0x043F: 'kk', 0x181A: 'sr-Latn-BA',
  0x1009: 'en-CA', 0x0453: 'km', 0x081A: 'sr-Latn',
  0x2409: 'en-029', 0x0486: 'quc', 0x046C: 'nso'
}];

var NameRecord = new r.Struct({
  platformID: r.uint16,
  encodingID: r.uint16,
  languageID: r.uint16,
  nameID: r.uint16,
  length: r.uint16,
  string: new r.Pointer(r.uint16, new r.String('length', function (t) {
    return getEncoding(t.platformID, t.encodingID, t.languageID);
  }), { type: 'parent', relativeTo: 'parent.stringOffset', allowNull: false })
});

var LangTagRecord = new r.Struct({
  length: r.uint16,
  tag: new r.Pointer(r.uint16, new r.String('length', 'utf16be'), { type: 'parent', relativeTo: 'stringOffset' })
});

var NameTable = new r.VersionedStruct(r.uint16, {
  0: {
    count: r.uint16,
    stringOffset: r.uint16,
    records: new r.Array(NameRecord, 'count')
  },
  1: {
    count: r.uint16,
    stringOffset: r.uint16,
    records: new r.Array(NameRecord, 'count'),
    langTagCount: r.uint16,
    langTags: new r.Array(LangTagRecord, 'langTagCount')
  }
});

var NAMES = ['copyright', 'fontFamily', 'fontSubfamily', 'uniqueSubfamily', 'fullName', 'version', 'postscriptName', // Note: A font may have only one PostScript name and that name must be ASCII.
'trademark', 'manufacturer', 'designer', 'description', 'vendorURL', 'designerURL', 'license', 'licenseURL', null, // reserved
'preferredFamily', 'preferredSubfamily', 'compatibleFull', 'sampleText', 'postscriptCIDFontName', 'wwsFamilyName', 'wwsSubfamilyName'];

NameTable.process = function (stream) {
  var records = {};
  for (var _iterator = this.records, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var record = _ref;

    // find out what language this is for
    var language = LANGUAGES[record.platformID][record.languageID];

    if (language == null && this.langTags != null && record.languageID >= 0x8000) {
      language = this.langTags[record.languageID - 0x8000].tag;
    }

    if (language == null) {
      language = record.platformID + '-' + record.languageID;
    }

    // if the nameID is >= 256, it is a font feature record (AAT)
    var key = record.nameID >= 256 ? 'fontFeatures' : NAMES[record.nameID] || record.nameID;
    if (records[key] == null) {
      records[key] = {};
    }

    var obj = records[key];
    if (record.nameID >= 256) {
      obj = obj[record.nameID] || (obj[record.nameID] = {});
    }

    if (typeof record.string === 'string' || typeof obj[language] !== 'string') {
      obj[language] = record.string;
    }
  }

  this.records = records;
};

NameTable.preEncode = function () {
  if (Array.isArray(this.records)) return;
  this.version = 0;

  var records = [];
  for (var key in this.records) {
    var val = this.records[key];
    if (key === 'fontFeatures') continue;

    records.push({
      platformID: 3,
      encodingID: 1,
      languageID: 0x409,
      nameID: NAMES.indexOf(key),
      length: Buffer.byteLength(val.en, 'utf16le'),
      string: val.en
    });

    if (key === 'postscriptName') {
      records.push({
        platformID: 1,
        encodingID: 0,
        languageID: 0,
        nameID: NAMES.indexOf(key),
        length: val.en.length,
        string: val.en
      });
    }
  }

  this.records = records;
  this.count = records.length;
  this.stringOffset = NameTable.size(this, null, false);
};

var OS2 = new r.VersionedStruct(r.uint16, {
  header: {
    xAvgCharWidth: r.int16, // average weighted advance width of lower case letters and space
    usWeightClass: r.uint16, // visual weight of stroke in glyphs
    usWidthClass: r.uint16, // relative change from the normal aspect ratio (width to height ratio)
    fsType: new r.Bitfield(r.uint16, [// Indicates font embedding licensing rights
    null, 'noEmbedding', 'viewOnly', 'editable', null, null, null, null, 'noSubsetting', 'bitmapOnly']),
    ySubscriptXSize: r.int16, // recommended horizontal size in pixels for subscripts
    ySubscriptYSize: r.int16, // recommended vertical size in pixels for subscripts
    ySubscriptXOffset: r.int16, // recommended horizontal offset for subscripts
    ySubscriptYOffset: r.int16, // recommended vertical offset form the baseline for subscripts
    ySuperscriptXSize: r.int16, // recommended horizontal size in pixels for superscripts
    ySuperscriptYSize: r.int16, // recommended vertical size in pixels for superscripts
    ySuperscriptXOffset: r.int16, // recommended horizontal offset for superscripts
    ySuperscriptYOffset: r.int16, // recommended vertical offset from the baseline for superscripts
    yStrikeoutSize: r.int16, // width of the strikeout stroke
    yStrikeoutPosition: r.int16, // position of the strikeout stroke relative to the baseline
    sFamilyClass: r.int16, // classification of font-family design
    panose: new r.Array(r.uint8, 10), // describe the visual characteristics of a given typeface
    ulCharRange: new r.Array(r.uint32, 4),
    vendorID: new r.String(4), // four character identifier for the font vendor
    fsSelection: new r.Bitfield(r.uint16, [// bit field containing information about the font
    'italic', 'underscore', 'negative', 'outlined', 'strikeout', 'bold', 'regular', 'useTypoMetrics', 'wws', 'oblique']),
    usFirstCharIndex: r.uint16, // The minimum Unicode index in this font
    usLastCharIndex: r.uint16 // The maximum Unicode index in this font
  },

  // The Apple version of this table ends here, but the Microsoft one continues on...
  0: {},

  1: {
    typoAscender: r.int16,
    typoDescender: r.int16,
    typoLineGap: r.int16,
    winAscent: r.uint16,
    winDescent: r.uint16,
    codePageRange: new r.Array(r.uint32, 2)
  },

  2: {
    // these should be common with version 1 somehow
    typoAscender: r.int16,
    typoDescender: r.int16,
    typoLineGap: r.int16,
    winAscent: r.uint16,
    winDescent: r.uint16,
    codePageRange: new r.Array(r.uint32, 2),

    xHeight: r.int16,
    capHeight: r.int16,
    defaultChar: r.uint16,
    breakChar: r.uint16,
    maxContent: r.uint16
  },

  5: {
    typoAscender: r.int16,
    typoDescender: r.int16,
    typoLineGap: r.int16,
    winAscent: r.uint16,
    winDescent: r.uint16,
    codePageRange: new r.Array(r.uint32, 2),

    xHeight: r.int16,
    capHeight: r.int16,
    defaultChar: r.uint16,
    breakChar: r.uint16,
    maxContent: r.uint16,

    usLowerOpticalPointSize: r.uint16,
    usUpperOpticalPointSize: r.uint16
  }
});

var versions = OS2.versions;
versions[3] = versions[4] = versions[2];

// PostScript information
var post = new r.VersionedStruct(r.fixed32, {
  header: { // these fields exist at the top of all versions
    italicAngle: r.fixed32, // Italic angle in counter-clockwise degrees from the vertical.
    underlinePosition: r.int16, // Suggested distance of the top of the underline from the baseline
    underlineThickness: r.int16, // Suggested values for the underline thickness
    isFixedPitch: r.uint32, // Whether the font is monospaced
    minMemType42: r.uint32, // Minimum memory usage when a TrueType font is downloaded as a Type 42 font
    maxMemType42: r.uint32, // Maximum memory usage when a TrueType font is downloaded as a Type 42 font
    minMemType1: r.uint32, // Minimum memory usage when a TrueType font is downloaded as a Type 1 font
    maxMemType1: r.uint32 // Maximum memory usage when a TrueType font is downloaded as a Type 1 font
  },

  1: {}, // version 1 has no additional fields

  2: {
    numberOfGlyphs: r.uint16,
    glyphNameIndex: new r.Array(r.uint16, 'numberOfGlyphs'),
    names: new r.Array(new r.String(r.uint8))
  },

  2.5: {
    numberOfGlyphs: r.uint16,
    offsets: new r.Array(r.uint8, 'numberOfGlyphs')
  },

  3: {}, // version 3 has no additional fields

  4: {
    map: new r.Array(r.uint32, function (t) {
      return t.parent.maxp.numGlyphs;
    })
  }
});

// An array of predefined values accessible by instructions
var cvt = new r.Struct({
  controlValues: new r.Array(r.int16)
});

// A list of instructions that are executed once when a font is first used.
// These instructions are known as the font program. The main use of this table
// is for the definition of functions that are used in many different glyph programs.
var fpgm = new r.Struct({
  instructions: new r.Array(r.uint8)
});

var loca = new r.VersionedStruct('head.indexToLocFormat', {
  0: {
    offsets: new r.Array(r.uint16)
  },
  1: {
    offsets: new r.Array(r.uint32)
  }
});

loca.process = function () {
  if (this.version === 0) {
    for (var i = 0; i < this.offsets.length; i++) {
      this.offsets[i] <<= 1;
    }
  }
};

loca.preEncode = function () {
  if (this.version === 0) {
    for (var i = 0; i < this.offsets.length; i++) {
      this.offsets[i] >>>= 1;
    }
  }
};

// Set of instructions executed whenever the point size or font transformation change
var prep = new r.Struct({
  controlValueProgram: new r.Array(r.uint8)
});

// only used for encoding
var glyf = new r.Array(new r.Buffer());

var CFFIndex = function () {
  function CFFIndex(type) {
    _classCallCheck(this, CFFIndex);

    this.type = type;
  }

  CFFIndex.prototype.getCFFVersion = function getCFFVersion(ctx) {
    while (ctx && !ctx.hdrSize) {
      ctx = ctx.parent;
    }

    return ctx ? ctx.version : -1;
  };

  CFFIndex.prototype.decode = function decode(stream, parent) {
    var version = this.getCFFVersion(parent);
    var count = version >= 2 ? stream.readUInt32BE() : stream.readUInt16BE();

    if (count === 0) {
      return [];
    }

    var offSize = stream.readUInt8();
    var offsetType = void 0;
    if (offSize === 1) {
      offsetType = r.uint8;
    } else if (offSize === 2) {
      offsetType = r.uint16;
    } else if (offSize === 3) {
      offsetType = r.uint24;
    } else if (offSize === 4) {
      offsetType = r.uint32;
    } else {
      throw new Error("Bad offset size in CFFIndex: " + offSize + " " + stream.pos);
    }

    var ret = [];
    var startPos = stream.pos + (count + 1) * offSize - 1;

    var start = offsetType.decode(stream);
    for (var i = 0; i < count; i++) {
      var end = offsetType.decode(stream);

      if (this.type != null) {
        var pos = stream.pos;
        stream.pos = startPos + start;

        parent.length = end - start;
        ret.push(this.type.decode(stream, parent));
        stream.pos = pos;
      } else {
        ret.push({
          offset: startPos + start,
          length: end - start
        });
      }

      start = end;
    }

    stream.pos = startPos + start;
    return ret;
  };

  CFFIndex.prototype.size = function size(arr, parent) {
    var size = 2;
    if (arr.length === 0) {
      return size;
    }

    var type = this.type || new r.Buffer();

    // find maximum offset to detminine offset type
    var offset = 1;
    for (var i = 0; i < arr.length; i++) {
      var item = arr[i];
      offset += type.size(item, parent);
    }

    var offsetType = void 0;
    if (offset <= 0xff) {
      offsetType = r.uint8;
    } else if (offset <= 0xffff) {
      offsetType = r.uint16;
    } else if (offset <= 0xffffff) {
      offsetType = r.uint24;
    } else if (offset <= 0xffffffff) {
      offsetType = r.uint32;
    } else {
      throw new Error("Bad offset in CFFIndex");
    }

    size += 1 + offsetType.size() * (arr.length + 1);
    size += offset - 1;

    return size;
  };

  CFFIndex.prototype.encode = function encode(stream, arr, parent) {
    stream.writeUInt16BE(arr.length);
    if (arr.length === 0) {
      return;
    }

    var type = this.type || new r.Buffer();

    // find maximum offset to detminine offset type
    var sizes = [];
    var offset = 1;
    for (var _iterator = arr, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var item = _ref;

      var s = type.size(item, parent);
      sizes.push(s);
      offset += s;
    }

    var offsetType = void 0;
    if (offset <= 0xff) {
      offsetType = r.uint8;
    } else if (offset <= 0xffff) {
      offsetType = r.uint16;
    } else if (offset <= 0xffffff) {
      offsetType = r.uint24;
    } else if (offset <= 0xffffffff) {
      offsetType = r.uint32;
    } else {
      throw new Error("Bad offset in CFFIndex");
    }

    // write offset size
    stream.writeUInt8(offsetType.size());

    // write elements
    offset = 1;
    offsetType.encode(stream, offset);

    for (var _iterator2 = sizes, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
      var _ref2;

      if (_isArray2) {
        if (_i2 >= _iterator2.length) break;
        _ref2 = _iterator2[_i2++];
      } else {
        _i2 = _iterator2.next();
        if (_i2.done) break;
        _ref2 = _i2.value;
      }

      var size = _ref2;

      offset += size;
      offsetType.encode(stream, offset);
    }

    for (var _iterator3 = arr, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
      var _ref3;

      if (_isArray3) {
        if (_i3 >= _iterator3.length) break;
        _ref3 = _iterator3[_i3++];
      } else {
        _i3 = _iterator3.next();
        if (_i3.done) break;
        _ref3 = _i3.value;
      }

      var _item = _ref3;

      type.encode(stream, _item, parent);
    }

    return;
  };

  return CFFIndex;
}();

var FLOAT_EOF = 0xf;
var FLOAT_LOOKUP = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'E', 'E-', null, '-'];

var FLOAT_ENCODE_LOOKUP = {
  '.': 10,
  'E': 11,
  'E-': 12,
  '-': 14
};

var CFFOperand = function () {
  function CFFOperand() {
    _classCallCheck(this, CFFOperand);
  }

  CFFOperand.decode = function decode(stream, value) {
    if (32 <= value && value <= 246) {
      return value - 139;
    }

    if (247 <= value && value <= 250) {
      return (value - 247) * 256 + stream.readUInt8() + 108;
    }

    if (251 <= value && value <= 254) {
      return -(value - 251) * 256 - stream.readUInt8() - 108;
    }

    if (value === 28) {
      return stream.readInt16BE();
    }

    if (value === 29) {
      return stream.readInt32BE();
    }

    if (value === 30) {
      var str = '';
      while (true) {
        var b = stream.readUInt8();

        var n1 = b >> 4;
        if (n1 === FLOAT_EOF) {
          break;
        }
        str += FLOAT_LOOKUP[n1];

        var n2 = b & 15;
        if (n2 === FLOAT_EOF) {
          break;
        }
        str += FLOAT_LOOKUP[n2];
      }

      return parseFloat(str);
    }

    return null;
  };

  CFFOperand.size = function size(value) {
    // if the value needs to be forced to the largest size (32 bit)
    // e.g. for unknown pointers, set to 32768
    if (value.forceLarge) {
      value = 32768;
    }

    if ((value | 0) !== value) {
      // floating point
      var str = '' + value;
      return 1 + Math.ceil((str.length + 1) / 2);
    } else if (-107 <= value && value <= 107) {
      return 1;
    } else if (108 <= value && value <= 1131 || -1131 <= value && value <= -108) {
      return 2;
    } else if (-32768 <= value && value <= 32767) {
      return 3;
    } else {
      return 5;
    }
  };

  CFFOperand.encode = function encode(stream, value) {
    // if the value needs to be forced to the largest size (32 bit)
    // e.g. for unknown pointers, save the old value and set to 32768
    var val = Number(value);

    if (value.forceLarge) {
      stream.writeUInt8(29);
      return stream.writeInt32BE(val);
    } else if ((val | 0) !== val) {
      // floating point
      stream.writeUInt8(30);

      var str = '' + val;
      for (var i = 0; i < str.length; i += 2) {
        var c1 = str[i];
        var n1 = FLOAT_ENCODE_LOOKUP[c1] || +c1;

        if (i === str.length - 1) {
          var n2 = FLOAT_EOF;
        } else {
          var c2 = str[i + 1];
          var n2 = FLOAT_ENCODE_LOOKUP[c2] || +c2;
        }

        stream.writeUInt8(n1 << 4 | n2 & 15);
      }

      if (n2 !== FLOAT_EOF) {
        return stream.writeUInt8(FLOAT_EOF << 4);
      }
    } else if (-107 <= val && val <= 107) {
      return stream.writeUInt8(val + 139);
    } else if (108 <= val && val <= 1131) {
      val -= 108;
      stream.writeUInt8((val >> 8) + 247);
      return stream.writeUInt8(val & 0xff);
    } else if (-1131 <= val && val <= -108) {
      val = -val - 108;
      stream.writeUInt8((val >> 8) + 251);
      return stream.writeUInt8(val & 0xff);
    } else if (-32768 <= val && val <= 32767) {
      stream.writeUInt8(28);
      return stream.writeInt16BE(val);
    } else {
      stream.writeUInt8(29);
      return stream.writeInt32BE(val);
    }
  };

  return CFFOperand;
}();

var CFFDict = function () {
  function CFFDict() {
    var ops = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

    _classCallCheck(this, CFFDict);

    this.ops = ops;
    this.fields = {};
    for (var _iterator = ops, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var field = _ref;

      var key = Array.isArray(field[0]) ? field[0][0] << 8 | field[0][1] : field[0];
      this.fields[key] = field;
    }
  }

  CFFDict.prototype.decodeOperands = function decodeOperands(type, stream, ret, operands) {
    var _this = this;

    if (Array.isArray(type)) {
      return operands.map(function (op, i) {
        return _this.decodeOperands(type[i], stream, ret, [op]);
      });
    } else if (type.decode != null) {
      return type.decode(stream, ret, operands);
    } else {
      switch (type) {
        case 'number':
        case 'offset':
        case 'sid':
          return operands[0];
        case 'boolean':
          return !!operands[0];
        default:
          return operands;
      }
    }
  };

  CFFDict.prototype.encodeOperands = function encodeOperands(type, stream, ctx, operands) {
    var _this2 = this;

    if (Array.isArray(type)) {
      return operands.map(function (op, i) {
        return _this2.encodeOperands(type[i], stream, ctx, op)[0];
      });
    } else if (type.encode != null) {
      return type.encode(stream, operands, ctx);
    } else if (typeof operands === 'number') {
      return [operands];
    } else if (typeof operands === 'boolean') {
      return [+operands];
    } else if (Array.isArray(operands)) {
      return operands;
    } else {
      return [operands];
    }
  };

  CFFDict.prototype.decode = function decode(stream, parent) {
    var end = stream.pos + parent.length;
    var ret = {};
    var operands = [];

    // define hidden properties
    _Object$defineProperties(ret, {
      parent: { value: parent },
      _startOffset: { value: stream.pos }
    });

    // fill in defaults
    for (var key in this.fields) {
      var field = this.fields[key];
      ret[field[1]] = field[3];
    }

    while (stream.pos < end) {
      var b = stream.readUInt8();
      if (b < 28) {
        if (b === 12) {
          b = b << 8 | stream.readUInt8();
        }

        var _field = this.fields[b];
        if (!_field) {
          throw new Error('Unknown operator ' + b);
        }

        var val = this.decodeOperands(_field[2], stream, ret, operands);
        if (val != null) {
          if (val instanceof restructure_src_utils.PropertyDescriptor) {
            _Object$defineProperty(ret, _field[1], val);
          } else {
            ret[_field[1]] = val;
          }
        }

        operands = [];
      } else {
        operands.push(CFFOperand.decode(stream, b));
      }
    }

    return ret;
  };

  CFFDict.prototype.size = function size(dict, parent) {
    var includePointers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

    var ctx = {
      parent: parent,
      val: dict,
      pointerSize: 0,
      startOffset: parent.startOffset || 0
    };

    var len = 0;

    for (var k in this.fields) {
      var field = this.fields[k];
      var val = dict[field[1]];
      if (val == null || isEqual(val, field[3])) {
        continue;
      }

      var operands = this.encodeOperands(field[2], null, ctx, val);
      for (var _iterator2 = operands, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
        var _ref2;

        if (_isArray2) {
          if (_i2 >= _iterator2.length) break;
          _ref2 = _iterator2[_i2++];
        } else {
          _i2 = _iterator2.next();
          if (_i2.done) break;
          _ref2 = _i2.value;
        }

        var op = _ref2;

        len += CFFOperand.size(op);
      }

      var key = Array.isArray(field[0]) ? field[0] : [field[0]];
      len += key.length;
    }

    if (includePointers) {
      len += ctx.pointerSize;
    }

    return len;
  };

  CFFDict.prototype.encode = function encode(stream, dict, parent) {
    var ctx = {
      pointers: [],
      startOffset: stream.pos,
      parent: parent,
      val: dict,
      pointerSize: 0
    };

    ctx.pointerOffset = stream.pos + this.size(dict, ctx, false);

    for (var _iterator3 = this.ops, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
      var _ref3;

      if (_isArray3) {
        if (_i3 >= _iterator3.length) break;
        _ref3 = _iterator3[_i3++];
      } else {
        _i3 = _iterator3.next();
        if (_i3.done) break;
        _ref3 = _i3.value;
      }

      var field = _ref3;

      var val = dict[field[1]];
      if (val == null || isEqual(val, field[3])) {
        continue;
      }

      var operands = this.encodeOperands(field[2], stream, ctx, val);
      for (var _iterator4 = operands, _isArray4 = Array.isArray(_iterator4), _i4 = 0, _iterator4 = _isArray4 ? _iterator4 : _getIterator(_iterator4);;) {
        var _ref4;

        if (_isArray4) {
          if (_i4 >= _iterator4.length) break;
          _ref4 = _iterator4[_i4++];
        } else {
          _i4 = _iterator4.next();
          if (_i4.done) break;
          _ref4 = _i4.value;
        }

        var op = _ref4;

        CFFOperand.encode(stream, op);
      }

      var key = Array.isArray(field[0]) ? field[0] : [field[0]];
      for (var _iterator5 = key, _isArray5 = Array.isArray(_iterator5), _i5 = 0, _iterator5 = _isArray5 ? _iterator5 : _getIterator(_iterator5);;) {
        var _ref5;

        if (_isArray5) {
          if (_i5 >= _iterator5.length) break;
          _ref5 = _iterator5[_i5++];
        } else {
          _i5 = _iterator5.next();
          if (_i5.done) break;
          _ref5 = _i5.value;
        }

        var _op = _ref5;

        stream.writeUInt8(_op);
      }
    }

    var i = 0;
    while (i < ctx.pointers.length) {
      var ptr = ctx.pointers[i++];
      ptr.type.encode(stream, ptr.val, ptr.parent);
    }

    return;
  };

  return CFFDict;
}();

var CFFPointer = function (_r$Pointer) {
  _inherits(CFFPointer, _r$Pointer);

  function CFFPointer(type) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    _classCallCheck(this, CFFPointer);

    if (options.type == null) {
      options.type = 'global';
    }

    return _possibleConstructorReturn(this, _r$Pointer.call(this, null, type, options));
  }

  CFFPointer.prototype.decode = function decode(stream, parent, operands) {
    this.offsetType = {
      decode: function decode() {
        return operands[0];
      }
    };

    return _r$Pointer.prototype.decode.call(this, stream, parent, operands);
  };

  CFFPointer.prototype.encode = function encode(stream, value, ctx) {
    if (!stream) {
      // compute the size (so ctx.pointerSize is correct)
      this.offsetType = {
        size: function size() {
          return 0;
        }
      };

      this.size(value, ctx);
      return [new Ptr(0)];
    }

    var ptr = null;
    this.offsetType = {
      encode: function encode(stream, val) {
        return ptr = val;
      }
    };

    _r$Pointer.prototype.encode.call(this, stream, value, ctx);
    return [new Ptr(ptr)];
  };

  return CFFPointer;
}(r.Pointer);

var Ptr = function () {
  function Ptr(val) {
    _classCallCheck(this, Ptr);

    this.val = val;
    this.forceLarge = true;
  }

  Ptr.prototype.valueOf = function valueOf() {
    return this.val;
  };

  return Ptr;
}();

var CFFBlendOp = function () {
  function CFFBlendOp() {
    _classCallCheck(this, CFFBlendOp);
  }

  CFFBlendOp.decode = function decode(stream, parent, operands) {
    var numBlends = operands.pop();

    // TODO: actually blend. For now just consume the deltas
    // since we don't use any of the values anyway.
    while (operands.length > numBlends) {
      operands.pop();
    }
  };

  return CFFBlendOp;
}();

var CFFPrivateDict = new CFFDict([
// key       name                    type                                          default
[6, 'BlueValues', 'delta', null], [7, 'OtherBlues', 'delta', null], [8, 'FamilyBlues', 'delta', null], [9, 'FamilyOtherBlues', 'delta', null], [[12, 9], 'BlueScale', 'number', 0.039625], [[12, 10], 'BlueShift', 'number', 7], [[12, 11], 'BlueFuzz', 'number', 1], [10, 'StdHW', 'number', null], [11, 'StdVW', 'number', null], [[12, 12], 'StemSnapH', 'delta', null], [[12, 13], 'StemSnapV', 'delta', null], [[12, 14], 'ForceBold', 'boolean', false], [[12, 17], 'LanguageGroup', 'number', 0], [[12, 18], 'ExpansionFactor', 'number', 0.06], [[12, 19], 'initialRandomSeed', 'number', 0], [20, 'defaultWidthX', 'number', 0], [21, 'nominalWidthX', 'number', 0], [22, 'vsindex', 'number', 0], [23, 'blend', CFFBlendOp, null], [19, 'Subrs', new CFFPointer(new CFFIndex(), { type: 'local' }), null]]);

// Automatically generated from Appendix A of the CFF specification; do
// not edit. Length should be 391.
var standardStrings = [".notdef", "space", "exclam", "quotedbl", "numbersign", "dollar", "percent", "ampersand", "quoteright", "parenleft", "parenright", "asterisk", "plus", "comma", "hyphen", "period", "slash", "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "colon", "semicolon", "less", "equal", "greater", "question", "at", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "bracketleft", "backslash", "bracketright", "asciicircum", "underscore", "quoteleft", "a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z", "braceleft", "bar", "braceright", "asciitilde", "exclamdown", "cent", "sterling", "fraction", "yen", "florin", "section", "currency", "quotesingle", "quotedblleft", "guillemotleft", "guilsinglleft", "guilsinglright", "fi", "fl", "endash", "dagger", "daggerdbl", "periodcentered", "paragraph", "bullet", "quotesinglbase", "quotedblbase", "quotedblright", "guillemotright", "ellipsis", "perthousand", "questiondown", "grave", "acute", "circumflex", "tilde", "macron", "breve", "dotaccent", "dieresis", "ring", "cedilla", "hungarumlaut", "ogonek", "caron", "emdash", "AE", "ordfeminine", "Lslash", "Oslash", "OE", "ordmasculine", "ae", "dotlessi", "lslash", "oslash", "oe", "germandbls", "onesuperior", "logicalnot", "mu", "trademark", "Eth", "onehalf", "plusminus", "Thorn", "onequarter", "divide", "brokenbar", "degree", "thorn", "threequarters", "twosuperior", "registered", "minus", "eth", "multiply", "threesuperior", "copyright", "Aacute", "Acircumflex", "Adieresis", "Agrave", "Aring", "Atilde", "Ccedilla", "Eacute", "Ecircumflex", "Edieresis", "Egrave", "Iacute", "Icircumflex", "Idieresis", "Igrave", "Ntilde", "Oacute", "Ocircumflex", "Odieresis", "Ograve", "Otilde", "Scaron", "Uacute", "Ucircumflex", "Udieresis", "Ugrave", "Yacute", "Ydieresis", "Zcaron", "aacute", "acircumflex", "adieresis", "agrave", "aring", "atilde", "ccedilla", "eacute", "ecircumflex", "edieresis", "egrave", "iacute", "icircumflex", "idieresis", "igrave", "ntilde", "oacute", "ocircumflex", "odieresis", "ograve", "otilde", "scaron", "uacute", "ucircumflex", "udieresis", "ugrave", "yacute", "ydieresis", "zcaron", "exclamsmall", "Hungarumlautsmall", "dollaroldstyle", "dollarsuperior", "ampersandsmall", "Acutesmall", "parenleftsuperior", "parenrightsuperior", "twodotenleader", "onedotenleader", "zerooldstyle", "oneoldstyle", "twooldstyle", "threeoldstyle", "fouroldstyle", "fiveoldstyle", "sixoldstyle", "sevenoldstyle", "eightoldstyle", "nineoldstyle", "commasuperior", "threequartersemdash", "periodsuperior", "questionsmall", "asuperior", "bsuperior", "centsuperior", "dsuperior", "esuperior", "isuperior", "lsuperior", "msuperior", "nsuperior", "osuperior", "rsuperior", "ssuperior", "tsuperior", "ff", "ffi", "ffl", "parenleftinferior", "parenrightinferior", "Circumflexsmall", "hyphensuperior", "Gravesmall", "Asmall", "Bsmall", "Csmall", "Dsmall", "Esmall", "Fsmall", "Gsmall", "Hsmall", "Ismall", "Jsmall", "Ksmall", "Lsmall", "Msmall", "Nsmall", "Osmall", "Psmall", "Qsmall", "Rsmall", "Ssmall", "Tsmall", "Usmall", "Vsmall", "Wsmall", "Xsmall", "Ysmall", "Zsmall", "colonmonetary", "onefitted", "rupiah", "Tildesmall", "exclamdownsmall", "centoldstyle", "Lslashsmall", "Scaronsmall", "Zcaronsmall", "Dieresissmall", "Brevesmall", "Caronsmall", "Dotaccentsmall", "Macronsmall", "figuredash", "hypheninferior", "Ogoneksmall", "Ringsmall", "Cedillasmall", "questiondownsmall", "oneeighth", "threeeighths", "fiveeighths", "seveneighths", "onethird", "twothirds", "zerosuperior", "foursuperior", "fivesuperior", "sixsuperior", "sevensuperior", "eightsuperior", "ninesuperior", "zeroinferior", "oneinferior", "twoinferior", "threeinferior", "fourinferior", "fiveinferior", "sixinferior", "seveninferior", "eightinferior", "nineinferior", "centinferior", "dollarinferior", "periodinferior", "commainferior", "Agravesmall", "Aacutesmall", "Acircumflexsmall", "Atildesmall", "Adieresissmall", "Aringsmall", "AEsmall", "Ccedillasmall", "Egravesmall", "Eacutesmall", "Ecircumflexsmall", "Edieresissmall", "Igravesmall", "Iacutesmall", "Icircumflexsmall", "Idieresissmall", "Ethsmall", "Ntildesmall", "Ogravesmall", "Oacutesmall", "Ocircumflexsmall", "Otildesmall", "Odieresissmall", "OEsmall", "Oslashsmall", "Ugravesmall", "Uacutesmall", "Ucircumflexsmall", "Udieresissmall", "Yacutesmall", "Thornsmall", "Ydieresissmall", "001.000", "001.001", "001.002", "001.003", "Black", "Bold", "Book", "Light", "Medium", "Regular", "Roman", "Semibold"];

var StandardEncoding = ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'space', 'exclam', 'quotedbl', 'numbersign', 'dollar', 'percent', 'ampersand', 'quoteright', 'parenleft', 'parenright', 'asterisk', 'plus', 'comma', 'hyphen', 'period', 'slash', 'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'colon', 'semicolon', 'less', 'equal', 'greater', 'question', 'at', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'bracketleft', 'backslash', 'bracketright', 'asciicircum', 'underscore', 'quoteleft', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'braceleft', 'bar', 'braceright', 'asciitilde', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'exclamdown', 'cent', 'sterling', 'fraction', 'yen', 'florin', 'section', 'currency', 'quotesingle', 'quotedblleft', 'guillemotleft', 'guilsinglleft', 'guilsinglright', 'fi', 'fl', '', 'endash', 'dagger', 'daggerdbl', 'periodcentered', '', 'paragraph', 'bullet', 'quotesinglbase', 'quotedblbase', 'quotedblright', 'guillemotright', 'ellipsis', 'perthousand', '', 'questiondown', '', 'grave', 'acute', 'circumflex', 'tilde', 'macron', 'breve', 'dotaccent', 'dieresis', '', 'ring', 'cedilla', '', 'hungarumlaut', 'ogonek', 'caron', 'emdash', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'AE', '', 'ordfeminine', '', '', '', '', 'Lslash', 'Oslash', 'OE', 'ordmasculine', '', '', '', '', '', 'ae', '', '', '', 'dotlessi', '', '', 'lslash', 'oslash', 'oe', 'germandbls'];

var ExpertEncoding = ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'space', 'exclamsmall', 'Hungarumlautsmall', '', 'dollaroldstyle', 'dollarsuperior', 'ampersandsmall', 'Acutesmall', 'parenleftsuperior', 'parenrightsuperior', 'twodotenleader', 'onedotenleader', 'comma', 'hyphen', 'period', 'fraction', 'zerooldstyle', 'oneoldstyle', 'twooldstyle', 'threeoldstyle', 'fouroldstyle', 'fiveoldstyle', 'sixoldstyle', 'sevenoldstyle', 'eightoldstyle', 'nineoldstyle', 'colon', 'semicolon', 'commasuperior', 'threequartersemdash', 'periodsuperior', 'questionsmall', '', 'asuperior', 'bsuperior', 'centsuperior', 'dsuperior', 'esuperior', '', '', 'isuperior', '', '', 'lsuperior', 'msuperior', 'nsuperior', 'osuperior', '', '', 'rsuperior', 'ssuperior', 'tsuperior', '', 'ff', 'fi', 'fl', 'ffi', 'ffl', 'parenleftinferior', '', 'parenrightinferior', 'Circumflexsmall', 'hyphensuperior', 'Gravesmall', 'Asmall', 'Bsmall', 'Csmall', 'Dsmall', 'Esmall', 'Fsmall', 'Gsmall', 'Hsmall', 'Ismall', 'Jsmall', 'Ksmall', 'Lsmall', 'Msmall', 'Nsmall', 'Osmall', 'Psmall', 'Qsmall', 'Rsmall', 'Ssmall', 'Tsmall', 'Usmall', 'Vsmall', 'Wsmall', 'Xsmall', 'Ysmall', 'Zsmall', 'colonmonetary', 'onefitted', 'rupiah', 'Tildesmall', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'exclamdownsmall', 'centoldstyle', 'Lslashsmall', '', '', 'Scaronsmall', 'Zcaronsmall', 'Dieresissmall', 'Brevesmall', 'Caronsmall', '', 'Dotaccentsmall', '', '', 'Macronsmall', '', '', 'figuredash', 'hypheninferior', '', '', 'Ogoneksmall', 'Ringsmall', 'Cedillasmall', '', '', '', 'onequarter', 'onehalf', 'threequarters', 'questiondownsmall', 'oneeighth', 'threeeighths', 'fiveeighths', 'seveneighths', 'onethird', 'twothirds', '', '', 'zerosuperior', 'onesuperior', 'twosuperior', 'threesuperior', 'foursuperior', 'fivesuperior', 'sixsuperior', 'sevensuperior', 'eightsuperior', 'ninesuperior', 'zeroinferior', 'oneinferior', 'twoinferior', 'threeinferior', 'fourinferior', 'fiveinferior', 'sixinferior', 'seveninferior', 'eightinferior', 'nineinferior', 'centinferior', 'dollarinferior', 'periodinferior', 'commainferior', 'Agravesmall', 'Aacutesmall', 'Acircumflexsmall', 'Atildesmall', 'Adieresissmall', 'Aringsmall', 'AEsmall', 'Ccedillasmall', 'Egravesmall', 'Eacutesmall', 'Ecircumflexsmall', 'Edieresissmall', 'Igravesmall', 'Iacutesmall', 'Icircumflexsmall', 'Idieresissmall', 'Ethsmall', 'Ntildesmall', 'Ogravesmall', 'Oacutesmall', 'Ocircumflexsmall', 'Otildesmall', 'Odieresissmall', 'OEsmall', 'Oslashsmall', 'Ugravesmall', 'Uacutesmall', 'Ucircumflexsmall', 'Udieresissmall', 'Yacutesmall', 'Thornsmall', 'Ydieresissmall'];

var ISOAdobeCharset = ['.notdef', 'space', 'exclam', 'quotedbl', 'numbersign', 'dollar', 'percent', 'ampersand', 'quoteright', 'parenleft', 'parenright', 'asterisk', 'plus', 'comma', 'hyphen', 'period', 'slash', 'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'colon', 'semicolon', 'less', 'equal', 'greater', 'question', 'at', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'bracketleft', 'backslash', 'bracketright', 'asciicircum', 'underscore', 'quoteleft', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'braceleft', 'bar', 'braceright', 'asciitilde', 'exclamdown', 'cent', 'sterling', 'fraction', 'yen', 'florin', 'section', 'currency', 'quotesingle', 'quotedblleft', 'guillemotleft', 'guilsinglleft', 'guilsinglright', 'fi', 'fl', 'endash', 'dagger', 'daggerdbl', 'periodcentered', 'paragraph', 'bullet', 'quotesinglbase', 'quotedblbase', 'quotedblright', 'guillemotright', 'ellipsis', 'perthousand', 'questiondown', 'grave', 'acute', 'circumflex', 'tilde', 'macron', 'breve', 'dotaccent', 'dieresis', 'ring', 'cedilla', 'hungarumlaut', 'ogonek', 'caron', 'emdash', 'AE', 'ordfeminine', 'Lslash', 'Oslash', 'OE', 'ordmasculine', 'ae', 'dotlessi', 'lslash', 'oslash', 'oe', 'germandbls', 'onesuperior', 'logicalnot', 'mu', 'trademark', 'Eth', 'onehalf', 'plusminus', 'Thorn', 'onequarter', 'divide', 'brokenbar', 'degree', 'thorn', 'threequarters', 'twosuperior', 'registered', 'minus', 'eth', 'multiply', 'threesuperior', 'copyright', 'Aacute', 'Acircumflex', 'Adieresis', 'Agrave', 'Aring', 'Atilde', 'Ccedilla', 'Eacute', 'Ecircumflex', 'Edieresis', 'Egrave', 'Iacute', 'Icircumflex', 'Idieresis', 'Igrave', 'Ntilde', 'Oacute', 'Ocircumflex', 'Odieresis', 'Ograve', 'Otilde', 'Scaron', 'Uacute', 'Ucircumflex', 'Udieresis', 'Ugrave', 'Yacute', 'Ydieresis', 'Zcaron', 'aacute', 'acircumflex', 'adieresis', 'agrave', 'aring', 'atilde', 'ccedilla', 'eacute', 'ecircumflex', 'edieresis', 'egrave', 'iacute', 'icircumflex', 'idieresis', 'igrave', 'ntilde', 'oacute', 'ocircumflex', 'odieresis', 'ograve', 'otilde', 'scaron', 'uacute', 'ucircumflex', 'udieresis', 'ugrave', 'yacute', 'ydieresis', 'zcaron'];

var ExpertCharset = ['.notdef', 'space', 'exclamsmall', 'Hungarumlautsmall', 'dollaroldstyle', 'dollarsuperior', 'ampersandsmall', 'Acutesmall', 'parenleftsuperior', 'parenrightsuperior', 'twodotenleader', 'onedotenleader', 'comma', 'hyphen', 'period', 'fraction', 'zerooldstyle', 'oneoldstyle', 'twooldstyle', 'threeoldstyle', 'fouroldstyle', 'fiveoldstyle', 'sixoldstyle', 'sevenoldstyle', 'eightoldstyle', 'nineoldstyle', 'colon', 'semicolon', 'commasuperior', 'threequartersemdash', 'periodsuperior', 'questionsmall', 'asuperior', 'bsuperior', 'centsuperior', 'dsuperior', 'esuperior', 'isuperior', 'lsuperior', 'msuperior', 'nsuperior', 'osuperior', 'rsuperior', 'ssuperior', 'tsuperior', 'ff', 'fi', 'fl', 'ffi', 'ffl', 'parenleftinferior', 'parenrightinferior', 'Circumflexsmall', 'hyphensuperior', 'Gravesmall', 'Asmall', 'Bsmall', 'Csmall', 'Dsmall', 'Esmall', 'Fsmall', 'Gsmall', 'Hsmall', 'Ismall', 'Jsmall', 'Ksmall', 'Lsmall', 'Msmall', 'Nsmall', 'Osmall', 'Psmall', 'Qsmall', 'Rsmall', 'Ssmall', 'Tsmall', 'Usmall', 'Vsmall', 'Wsmall', 'Xsmall', 'Ysmall', 'Zsmall', 'colonmonetary', 'onefitted', 'rupiah', 'Tildesmall', 'exclamdownsmall', 'centoldstyle', 'Lslashsmall', 'Scaronsmall', 'Zcaronsmall', 'Dieresissmall', 'Brevesmall', 'Caronsmall', 'Dotaccentsmall', 'Macronsmall', 'figuredash', 'hypheninferior', 'Ogoneksmall', 'Ringsmall', 'Cedillasmall', 'onequarter', 'onehalf', 'threequarters', 'questiondownsmall', 'oneeighth', 'threeeighths', 'fiveeighths', 'seveneighths', 'onethird', 'twothirds', 'zerosuperior', 'onesuperior', 'twosuperior', 'threesuperior', 'foursuperior', 'fivesuperior', 'sixsuperior', 'sevensuperior', 'eightsuperior', 'ninesuperior', 'zeroinferior', 'oneinferior', 'twoinferior', 'threeinferior', 'fourinferior', 'fiveinferior', 'sixinferior', 'seveninferior', 'eightinferior', 'nineinferior', 'centinferior', 'dollarinferior', 'periodinferior', 'commainferior', 'Agravesmall', 'Aacutesmall', 'Acircumflexsmall', 'Atildesmall', 'Adieresissmall', 'Aringsmall', 'AEsmall', 'Ccedillasmall', 'Egravesmall', 'Eacutesmall', 'Ecircumflexsmall', 'Edieresissmall', 'Igravesmall', 'Iacutesmall', 'Icircumflexsmall', 'Idieresissmall', 'Ethsmall', 'Ntildesmall', 'Ogravesmall', 'Oacutesmall', 'Ocircumflexsmall', 'Otildesmall', 'Odieresissmall', 'OEsmall', 'Oslashsmall', 'Ugravesmall', 'Uacutesmall', 'Ucircumflexsmall', 'Udieresissmall', 'Yacutesmall', 'Thornsmall', 'Ydieresissmall'];

var ExpertSubsetCharset = ['.notdef', 'space', 'dollaroldstyle', 'dollarsuperior', 'parenleftsuperior', 'parenrightsuperior', 'twodotenleader', 'onedotenleader', 'comma', 'hyphen', 'period', 'fraction', 'zerooldstyle', 'oneoldstyle', 'twooldstyle', 'threeoldstyle', 'fouroldstyle', 'fiveoldstyle', 'sixoldstyle', 'sevenoldstyle', 'eightoldstyle', 'nineoldstyle', 'colon', 'semicolon', 'commasuperior', 'threequartersemdash', 'periodsuperior', 'asuperior', 'bsuperior', 'centsuperior', 'dsuperior', 'esuperior', 'isuperior', 'lsuperior', 'msuperior', 'nsuperior', 'osuperior', 'rsuperior', 'ssuperior', 'tsuperior', 'ff', 'fi', 'fl', 'ffi', 'ffl', 'parenleftinferior', 'parenrightinferior', 'hyphensuperior', 'colonmonetary', 'onefitted', 'rupiah', 'centoldstyle', 'figuredash', 'hypheninferior', 'onequarter', 'onehalf', 'threequarters', 'oneeighth', 'threeeighths', 'fiveeighths', 'seveneighths', 'onethird', 'twothirds', 'zerosuperior', 'onesuperior', 'twosuperior', 'threesuperior', 'foursuperior', 'fivesuperior', 'sixsuperior', 'sevensuperior', 'eightsuperior', 'ninesuperior', 'zeroinferior', 'oneinferior', 'twoinferior', 'threeinferior', 'fourinferior', 'fiveinferior', 'sixinferior', 'seveninferior', 'eightinferior', 'nineinferior', 'centinferior', 'dollarinferior', 'periodinferior', 'commainferior'];

//########################
// Scripts and Languages #
//########################

var LangSysTable = new r.Struct({
  reserved: new r.Reserved(r.uint16),
  reqFeatureIndex: r.uint16,
  featureCount: r.uint16,
  featureIndexes: new r.Array(r.uint16, 'featureCount')
});

var LangSysRecord = new r.Struct({
  tag: new r.String(4),
  langSys: new r.Pointer(r.uint16, LangSysTable, { type: 'parent' })
});

var Script = new r.Struct({
  defaultLangSys: new r.Pointer(r.uint16, LangSysTable),
  count: r.uint16,
  langSysRecords: new r.Array(LangSysRecord, 'count')
});

var ScriptRecord = new r.Struct({
  tag: new r.String(4),
  script: new r.Pointer(r.uint16, Script, { type: 'parent' })
});

var ScriptList = new r.Array(ScriptRecord, r.uint16);

//#######################
// Features and Lookups #
//#######################

var Feature = new r.Struct({
  featureParams: r.uint16, // pointer
  lookupCount: r.uint16,
  lookupListIndexes: new r.Array(r.uint16, 'lookupCount')
});

var FeatureRecord = new r.Struct({
  tag: new r.String(4),
  feature: new r.Pointer(r.uint16, Feature, { type: 'parent' })
});

var FeatureList = new r.Array(FeatureRecord, r.uint16);

var LookupFlags = new r.Struct({
  markAttachmentType: r.uint8,
  flags: new r.Bitfield(r.uint8, ['rightToLeft', 'ignoreBaseGlyphs', 'ignoreLigatures', 'ignoreMarks', 'useMarkFilteringSet'])
});

function LookupList(SubTable) {
  var Lookup = new r.Struct({
    lookupType: r.uint16,
    flags: LookupFlags,
    subTableCount: r.uint16,
    subTables: new r.Array(new r.Pointer(r.uint16, SubTable), 'subTableCount'),
    markFilteringSet: new r.Optional(r.uint16, function (t) {
      return t.flags.flags.useMarkFilteringSet;
    })
  });

  return new r.LazyArray(new r.Pointer(r.uint16, Lookup), r.uint16);
}

//#################
// Coverage Table #
//#################

var RangeRecord = new r.Struct({
  start: r.uint16,
  end: r.uint16,
  startCoverageIndex: r.uint16
});

var Coverage = new r.VersionedStruct(r.uint16, {
  1: {
    glyphCount: r.uint16,
    glyphs: new r.Array(r.uint16, 'glyphCount')
  },
  2: {
    rangeCount: r.uint16,
    rangeRecords: new r.Array(RangeRecord, 'rangeCount')
  }
});

//#########################
// Class Definition Table #
//#########################

var ClassRangeRecord = new r.Struct({
  start: r.uint16,
  end: r.uint16,
  class: r.uint16
});

var ClassDef = new r.VersionedStruct(r.uint16, {
  1: { // Class array
    startGlyph: r.uint16,
    glyphCount: r.uint16,
    classValueArray: new r.Array(r.uint16, 'glyphCount')
  },
  2: { // Class ranges
    classRangeCount: r.uint16,
    classRangeRecord: new r.Array(ClassRangeRecord, 'classRangeCount')
  }
});

//###############
// Device Table #
//###############

var Device = new r.Struct({
  a: r.uint16, // startSize for hinting Device, outerIndex for VariationIndex
  b: r.uint16, // endSize for Device, innerIndex for VariationIndex
  deltaFormat: r.uint16
});

//#############################################
// Contextual Substitution/Positioning Tables #
//#############################################

var LookupRecord = new r.Struct({
  sequenceIndex: r.uint16,
  lookupListIndex: r.uint16
});

var Rule = new r.Struct({
  glyphCount: r.uint16,
  lookupCount: r.uint16,
  input: new r.Array(r.uint16, function (t) {
    return t.glyphCount - 1;
  }),
  lookupRecords: new r.Array(LookupRecord, 'lookupCount')
});

var RuleSet = new r.Array(new r.Pointer(r.uint16, Rule), r.uint16);

var ClassRule = new r.Struct({
  glyphCount: r.uint16,
  lookupCount: r.uint16,
  classes: new r.Array(r.uint16, function (t) {
    return t.glyphCount - 1;
  }),
  lookupRecords: new r.Array(LookupRecord, 'lookupCount')
});

var ClassSet = new r.Array(new r.Pointer(r.uint16, ClassRule), r.uint16);

var Context = new r.VersionedStruct(r.uint16, {
  1: { // Simple context
    coverage: new r.Pointer(r.uint16, Coverage),
    ruleSetCount: r.uint16,
    ruleSets: new r.Array(new r.Pointer(r.uint16, RuleSet), 'ruleSetCount')
  },
  2: { // Class-based context
    coverage: new r.Pointer(r.uint16, Coverage),
    classDef: new r.Pointer(r.uint16, ClassDef),
    classSetCnt: r.uint16,
    classSet: new r.Array(new r.Pointer(r.uint16, ClassSet), 'classSetCnt')
  },
  3: {
    glyphCount: r.uint16,
    lookupCount: r.uint16,
    coverages: new r.Array(new r.Pointer(r.uint16, Coverage), 'glyphCount'),
    lookupRecords: new r.Array(LookupRecord, 'lookupCount')
  }
});

//######################################################
// Chaining Contextual Substitution/Positioning Tables #
//######################################################

var ChainRule = new r.Struct({
  backtrackGlyphCount: r.uint16,
  backtrack: new r.Array(r.uint16, 'backtrackGlyphCount'),
  inputGlyphCount: r.uint16,
  input: new r.Array(r.uint16, function (t) {
    return t.inputGlyphCount - 1;
  }),
  lookaheadGlyphCount: r.uint16,
  lookahead: new r.Array(r.uint16, 'lookaheadGlyphCount'),
  lookupCount: r.uint16,
  lookupRecords: new r.Array(LookupRecord, 'lookupCount')
});

var ChainRuleSet = new r.Array(new r.Pointer(r.uint16, ChainRule), r.uint16);

var ChainingContext = new r.VersionedStruct(r.uint16, {
  1: { // Simple context glyph substitution
    coverage: new r.Pointer(r.uint16, Coverage),
    chainCount: r.uint16,
    chainRuleSets: new r.Array(new r.Pointer(r.uint16, ChainRuleSet), 'chainCount')
  },

  2: { // Class-based chaining context
    coverage: new r.Pointer(r.uint16, Coverage),
    backtrackClassDef: new r.Pointer(r.uint16, ClassDef),
    inputClassDef: new r.Pointer(r.uint16, ClassDef),
    lookaheadClassDef: new r.Pointer(r.uint16, ClassDef),
    chainCount: r.uint16,
    chainClassSet: new r.Array(new r.Pointer(r.uint16, ChainRuleSet), 'chainCount')
  },

  3: { // Coverage-based chaining context
    backtrackGlyphCount: r.uint16,
    backtrackCoverage: new r.Array(new r.Pointer(r.uint16, Coverage), 'backtrackGlyphCount'),
    inputGlyphCount: r.uint16,
    inputCoverage: new r.Array(new r.Pointer(r.uint16, Coverage), 'inputGlyphCount'),
    lookaheadGlyphCount: r.uint16,
    lookaheadCoverage: new r.Array(new r.Pointer(r.uint16, Coverage), 'lookaheadGlyphCount'),
    lookupCount: r.uint16,
    lookupRecords: new r.Array(LookupRecord, 'lookupCount')
  }
});

var _;

/*******************
 * Variation Store *
 *******************/

var F2DOT14 = new r.Fixed(16, 'BE', 14);
var RegionAxisCoordinates = new r.Struct({
  startCoord: F2DOT14,
  peakCoord: F2DOT14,
  endCoord: F2DOT14
});

var VariationRegionList = new r.Struct({
  axisCount: r.uint16,
  regionCount: r.uint16,
  variationRegions: new r.Array(new r.Array(RegionAxisCoordinates, 'axisCount'), 'regionCount')
});

var DeltaSet = new r.Struct({
  shortDeltas: new r.Array(r.int16, function (t) {
    return t.parent.shortDeltaCount;
  }),
  regionDeltas: new r.Array(r.int8, function (t) {
    return t.parent.regionIndexCount - t.parent.shortDeltaCount;
  }),
  deltas: function deltas(t) {
    return t.shortDeltas.concat(t.regionDeltas);
  }
});

var ItemVariationData = new r.Struct({
  itemCount: r.uint16,
  shortDeltaCount: r.uint16,
  regionIndexCount: r.uint16,
  regionIndexes: new r.Array(r.uint16, 'regionIndexCount'),
  deltaSets: new r.Array(DeltaSet, 'itemCount')
});

var ItemVariationStore = new r.Struct({
  format: r.uint16,
  variationRegionList: new r.Pointer(r.uint32, VariationRegionList),
  variationDataCount: r.uint16,
  itemVariationData: new r.Array(new r.Pointer(r.uint32, ItemVariationData), 'variationDataCount')
});

/**********************
 * Feature Variations *
 **********************/

var ConditionTable = new r.VersionedStruct(r.uint16, {
  1: (_ = {
    axisIndex: r.uint16
  }, _['axisIndex'] = r.uint16, _.filterRangeMinValue = F2DOT14, _.filterRangeMaxValue = F2DOT14, _)
});

var ConditionSet = new r.Struct({
  conditionCount: r.uint16,
  conditionTable: new r.Array(new r.Pointer(r.uint32, ConditionTable), 'conditionCount')
});

var FeatureTableSubstitutionRecord = new r.Struct({
  featureIndex: r.uint16,
  alternateFeatureTable: new r.Pointer(r.uint32, Feature, { type: 'parent' })
});

var FeatureTableSubstitution = new r.Struct({
  version: r.fixed32,
  substitutionCount: r.uint16,
  substitutions: new r.Array(FeatureTableSubstitutionRecord, 'substitutionCount')
});

var FeatureVariationRecord = new r.Struct({
  conditionSet: new r.Pointer(r.uint32, ConditionSet, { type: 'parent' }),
  featureTableSubstitution: new r.Pointer(r.uint32, FeatureTableSubstitution, { type: 'parent' })
});

var FeatureVariations = new r.Struct({
  majorVersion: r.uint16,
  minorVersion: r.uint16,
  featureVariationRecordCount: r.uint32,
  featureVariationRecords: new r.Array(FeatureVariationRecord, 'featureVariationRecordCount')
});

// Checks if an operand is an index of a predefined value,
// otherwise delegates to the provided type.

var PredefinedOp = function () {
  function PredefinedOp(predefinedOps, type) {
    _classCallCheck(this, PredefinedOp);

    this.predefinedOps = predefinedOps;
    this.type = type;
  }

  PredefinedOp.prototype.decode = function decode(stream, parent, operands) {
    if (this.predefinedOps[operands[0]]) {
      return this.predefinedOps[operands[0]];
    }

    return this.type.decode(stream, parent, operands);
  };

  PredefinedOp.prototype.size = function size(value, ctx) {
    return this.type.size(value, ctx);
  };

  PredefinedOp.prototype.encode = function encode(stream, value, ctx) {
    var index = this.predefinedOps.indexOf(value);
    if (index !== -1) {
      return index;
    }

    return this.type.encode(stream, value, ctx);
  };

  return PredefinedOp;
}();

var CFFEncodingVersion = function (_r$Number) {
  _inherits(CFFEncodingVersion, _r$Number);

  function CFFEncodingVersion() {
    _classCallCheck(this, CFFEncodingVersion);

    return _possibleConstructorReturn(this, _r$Number.call(this, 'UInt8'));
  }

  CFFEncodingVersion.prototype.decode = function decode(stream) {
    return r.uint8.decode(stream) & 0x7f;
  };

  return CFFEncodingVersion;
}(r.Number);

var Range1 = new r.Struct({
  first: r.uint16,
  nLeft: r.uint8
});

var Range2 = new r.Struct({
  first: r.uint16,
  nLeft: r.uint16
});

var CFFCustomEncoding = new r.VersionedStruct(new CFFEncodingVersion(), {
  0: {
    nCodes: r.uint8,
    codes: new r.Array(r.uint8, 'nCodes')
  },

  1: {
    nRanges: r.uint8,
    ranges: new r.Array(Range1, 'nRanges')

    // TODO: supplement?
  } });

var CFFEncoding = new PredefinedOp([StandardEncoding, ExpertEncoding], new CFFPointer(CFFCustomEncoding, { lazy: true }));

// Decodes an array of ranges until the total
// length is equal to the provided length.

var RangeArray = function (_r$Array) {
  _inherits(RangeArray, _r$Array);

  function RangeArray() {
    _classCallCheck(this, RangeArray);

    return _possibleConstructorReturn(this, _r$Array.apply(this, arguments));
  }

  RangeArray.prototype.decode = function decode(stream, parent) {
    var length = restructure_src_utils.resolveLength(this.length, stream, parent);
    var count = 0;
    var res = [];
    while (count < length) {
      var range = this.type.decode(stream, parent);
      range.offset = count;
      count += range.nLeft + 1;
      res.push(range);
    }

    return res;
  };

  return RangeArray;
}(r.Array);

var CFFCustomCharset = new r.VersionedStruct(r.uint8, {
  0: {
    glyphs: new r.Array(r.uint16, function (t) {
      return t.parent.CharStrings.length - 1;
    })
  },

  1: {
    ranges: new RangeArray(Range1, function (t) {
      return t.parent.CharStrings.length - 1;
    })
  },

  2: {
    ranges: new RangeArray(Range2, function (t) {
      return t.parent.CharStrings.length - 1;
    })
  }
});

var CFFCharset = new PredefinedOp([ISOAdobeCharset, ExpertCharset, ExpertSubsetCharset], new CFFPointer(CFFCustomCharset, { lazy: true }));

var FDRange3 = new r.Struct({
  first: r.uint16,
  fd: r.uint8
});

var FDRange4 = new r.Struct({
  first: r.uint32,
  fd: r.uint16
});

var FDSelect = new r.VersionedStruct(r.uint8, {
  0: {
    fds: new r.Array(r.uint8, function (t) {
      return t.parent.CharStrings.length;
    })
  },

  3: {
    nRanges: r.uint16,
    ranges: new r.Array(FDRange3, 'nRanges'),
    sentinel: r.uint16
  },

  4: {
    nRanges: r.uint32,
    ranges: new r.Array(FDRange4, 'nRanges'),
    sentinel: r.uint32
  }
});

var ptr = new CFFPointer(CFFPrivateDict);

var CFFPrivateOp = function () {
  function CFFPrivateOp() {
    _classCallCheck(this, CFFPrivateOp);
  }

  CFFPrivateOp.prototype.decode = function decode(stream, parent, operands) {
    parent.length = operands[0];
    return ptr.decode(stream, parent, [operands[1]]);
  };

  CFFPrivateOp.prototype.size = function size(dict, ctx) {
    return [CFFPrivateDict.size(dict, ctx, false), ptr.size(dict, ctx)[0]];
  };

  CFFPrivateOp.prototype.encode = function encode(stream, dict, ctx) {
    return [CFFPrivateDict.size(dict, ctx, false), ptr.encode(stream, dict, ctx)[0]];
  };

  return CFFPrivateOp;
}();

var FontDict = new CFFDict([
// key       name                   type(s)                                 default
[18, 'Private', new CFFPrivateOp(), null], [[12, 38], 'FontName', 'sid', null], [[12, 7], 'FontMatrix', 'array', [0.001, 0, 0, 0.001, 0, 0]], [[12, 5], 'PaintType', 'number', 0]]);

var CFFTopDict = new CFFDict([
// key       name                   type(s)                                 default
[[12, 30], 'ROS', ['sid', 'sid', 'number'], null], [0, 'version', 'sid', null], [1, 'Notice', 'sid', null], [[12, 0], 'Copyright', 'sid', null], [2, 'FullName', 'sid', null], [3, 'FamilyName', 'sid', null], [4, 'Weight', 'sid', null], [[12, 1], 'isFixedPitch', 'boolean', false], [[12, 2], 'ItalicAngle', 'number', 0], [[12, 3], 'UnderlinePosition', 'number', -100], [[12, 4], 'UnderlineThickness', 'number', 50], [[12, 5], 'PaintType', 'number', 0], [[12, 6], 'CharstringType', 'number', 2], [[12, 7], 'FontMatrix', 'array', [0.001, 0, 0, 0.001, 0, 0]], [13, 'UniqueID', 'number', null], [5, 'FontBBox', 'array', [0, 0, 0, 0]], [[12, 8], 'StrokeWidth', 'number', 0], [14, 'XUID', 'array', null], [15, 'charset', CFFCharset, ISOAdobeCharset], [16, 'Encoding', CFFEncoding, StandardEncoding], [17, 'CharStrings', new CFFPointer(new CFFIndex()), null], [18, 'Private', new CFFPrivateOp(), null], [[12, 20], 'SyntheticBase', 'number', null], [[12, 21], 'PostScript', 'sid', null], [[12, 22], 'BaseFontName', 'sid', null], [[12, 23], 'BaseFontBlend', 'delta', null],

// CID font specific
[[12, 31], 'CIDFontVersion', 'number', 0], [[12, 32], 'CIDFontRevision', 'number', 0], [[12, 33], 'CIDFontType', 'number', 0], [[12, 34], 'CIDCount', 'number', 8720], [[12, 35], 'UIDBase', 'number', null], [[12, 37], 'FDSelect', new CFFPointer(FDSelect), null], [[12, 36], 'FDArray', new CFFPointer(new CFFIndex(FontDict)), null], [[12, 38], 'FontName', 'sid', null]]);

var VariationStore = new r.Struct({
  length: r.uint16,
  itemVariationStore: ItemVariationStore
});

var CFF2TopDict = new CFFDict([[[12, 7], 'FontMatrix', 'array', [0.001, 0, 0, 0.001, 0, 0]], [17, 'CharStrings', new CFFPointer(new CFFIndex()), null], [[12, 37], 'FDSelect', new CFFPointer(FDSelect), null], [[12, 36], 'FDArray', new CFFPointer(new CFFIndex(FontDict)), null], [24, 'vstore', new CFFPointer(VariationStore), null], [25, 'maxstack', 'number', 193]]);

var CFFTop = new r.VersionedStruct(r.fixed16, {
  1: {
    hdrSize: r.uint8,
    offSize: r.uint8,
    nameIndex: new CFFIndex(new r.String('length')),
    topDictIndex: new CFFIndex(CFFTopDict),
    stringIndex: new CFFIndex(new r.String('length')),
    globalSubrIndex: new CFFIndex()
  },

  2: {
    hdrSize: r.uint8,
    length: r.uint16,
    topDict: CFF2TopDict,
    globalSubrIndex: new CFFIndex()
  }
});

var CFFFont = function () {
  function CFFFont(stream) {
    _classCallCheck(this, CFFFont);

    this.stream = stream;
    this.decode();
  }

  CFFFont.decode = function decode(stream) {
    return new CFFFont(stream);
  };

  CFFFont.prototype.decode = function decode() {
    var start = this.stream.pos;
    var top = CFFTop.decode(this.stream);
    for (var key in top) {
      var val = top[key];
      this[key] = val;
    }

    if (this.version < 2) {
      if (this.topDictIndex.length !== 1) {
        throw new Error("Only a single font is allowed in CFF");
      }

      this.topDict = this.topDictIndex[0];
    }

    this.isCIDFont = this.topDict.ROS != null;
    return this;
  };

  CFFFont.prototype.string = function string(sid) {
    if (this.version >= 2) {
      return null;
    }

    if (sid < standardStrings.length) {
      return standardStrings[sid];
    }

    return this.stringIndex[sid - standardStrings.length];
  };

  CFFFont.prototype.getCharString = function getCharString(glyph) {
    this.stream.pos = this.topDict.CharStrings[glyph].offset;
    return this.stream.readBuffer(this.topDict.CharStrings[glyph].length);
  };

  CFFFont.prototype.getGlyphName = function getGlyphName(gid) {
    // CFF2 glyph names are in the post table.
    if (this.version >= 2) {
      return null;
    }

    // CID-keyed fonts don't have glyph names
    if (this.isCIDFont) {
      return null;
    }

    var charset = this.topDict.charset;

    if (Array.isArray(charset)) {
      return charset[gid];
    }

    if (gid === 0) {
      return '.notdef';
    }

    gid -= 1;

    switch (charset.version) {
      case 0:
        return this.string(charset.glyphs[gid]);

      case 1:
      case 2:
        for (var i = 0; i < charset.ranges.length; i++) {
          var range = charset.ranges[i];
          if (range.offset <= gid && gid <= range.offset + range.nLeft) {
            return this.string(range.first + (gid - range.offset));
          }
        }
        break;
    }

    return null;
  };

  CFFFont.prototype.fdForGlyph = function fdForGlyph(gid) {
    if (!this.topDict.FDSelect) {
      return null;
    }

    switch (this.topDict.FDSelect.version) {
      case 0:
        return this.topDict.FDSelect.fds[gid];

      case 3:
      case 4:
        var ranges = this.topDict.FDSelect.ranges;

        var low = 0;
        var high = ranges.length - 1;

        while (low <= high) {
          var mid = low + high >> 1;

          if (gid < ranges[mid].first) {
            high = mid - 1;
          } else if (mid < high && gid >= ranges[mid + 1].first) {
            low = mid + 1;
          } else {
            return ranges[mid].fd;
          }
        }
      default:
        throw new Error('Unknown FDSelect version: ' + this.topDict.FDSelect.version);
    }
  };

  CFFFont.prototype.privateDictForGlyph = function privateDictForGlyph(gid) {
    if (this.topDict.FDSelect) {
      var fd = this.fdForGlyph(gid);
      if (this.topDict.FDArray[fd]) {
        return this.topDict.FDArray[fd].Private;
      }

      return null;
    }

    if (this.version < 2) {
      return this.topDict.Private;
    }

    return this.topDict.FDArray[0].Private;
  };

  _createClass(CFFFont, [{
    key: 'postscriptName',
    get: function get() {
      if (this.version < 2) {
        return this.nameIndex[0];
      }

      return null;
    }
  }, {
    key: 'fullName',
    get: function get() {
      return this.string(this.topDict.FullName);
    }
  }, {
    key: 'familyName',
    get: function get() {
      return this.string(this.topDict.FamilyName);
    }
  }]);

  return CFFFont;
}();

var VerticalOrigin = new r.Struct({
  glyphIndex: r.uint16,
  vertOriginY: r.int16
});

var VORG = new r.Struct({
  majorVersion: r.uint16,
  minorVersion: r.uint16,
  defaultVertOriginY: r.int16,
  numVertOriginYMetrics: r.uint16,
  metrics: new r.Array(VerticalOrigin, 'numVertOriginYMetrics')
});

var BigMetrics = new r.Struct({
  height: r.uint8,
  width: r.uint8,
  horiBearingX: r.int8,
  horiBearingY: r.int8,
  horiAdvance: r.uint8,
  vertBearingX: r.int8,
  vertBearingY: r.int8,
  vertAdvance: r.uint8
});

var SmallMetrics = new r.Struct({
  height: r.uint8,
  width: r.uint8,
  bearingX: r.int8,
  bearingY: r.int8,
  advance: r.uint8
});

var EBDTComponent = new r.Struct({
  glyph: r.uint16,
  xOffset: r.int8,
  yOffset: r.int8
});

var ByteAligned = function ByteAligned() {
  _classCallCheck(this, ByteAligned);
};

var BitAligned = function BitAligned() {
  _classCallCheck(this, BitAligned);
};

var glyph = new r.VersionedStruct('version', {
  1: {
    metrics: SmallMetrics,
    data: ByteAligned
  },

  2: {
    metrics: SmallMetrics,
    data: BitAligned
  },

  // format 3 is deprecated
  // format 4 is not supported by Microsoft

  5: {
    data: BitAligned
  },

  6: {
    metrics: BigMetrics,
    data: ByteAligned
  },

  7: {
    metrics: BigMetrics,
    data: BitAligned
  },

  8: {
    metrics: SmallMetrics,
    pad: new r.Reserved(r.uint8),
    numComponents: r.uint16,
    components: new r.Array(EBDTComponent, 'numComponents')
  },

  9: {
    metrics: BigMetrics,
    pad: new r.Reserved(r.uint8),
    numComponents: r.uint16,
    components: new r.Array(EBDTComponent, 'numComponents')
  },

  17: {
    metrics: SmallMetrics,
    dataLen: r.uint32,
    data: new r.Buffer('dataLen')
  },

  18: {
    metrics: BigMetrics,
    dataLen: r.uint32,
    data: new r.Buffer('dataLen')
  },

  19: {
    dataLen: r.uint32,
    data: new r.Buffer('dataLen')
  }
});

var SBitLineMetrics = new r.Struct({
  ascender: r.int8,
  descender: r.int8,
  widthMax: r.uint8,
  caretSlopeNumerator: r.int8,
  caretSlopeDenominator: r.int8,
  caretOffset: r.int8,
  minOriginSB: r.int8,
  minAdvanceSB: r.int8,
  maxBeforeBL: r.int8,
  minAfterBL: r.int8,
  pad: new r.Reserved(r.int8, 2)
});

var CodeOffsetPair = new r.Struct({
  glyphCode: r.uint16,
  offset: r.uint16
});

var IndexSubtable = new r.VersionedStruct(r.uint16, {
  header: {
    imageFormat: r.uint16,
    imageDataOffset: r.uint32
  },

  1: {
    offsetArray: new r.Array(r.uint32, function (t) {
      return t.parent.lastGlyphIndex - t.parent.firstGlyphIndex + 1;
    })
  },

  2: {
    imageSize: r.uint32,
    bigMetrics: BigMetrics
  },

  3: {
    offsetArray: new r.Array(r.uint16, function (t) {
      return t.parent.lastGlyphIndex - t.parent.firstGlyphIndex + 1;
    })
  },

  4: {
    numGlyphs: r.uint32,
    glyphArray: new r.Array(CodeOffsetPair, function (t) {
      return t.numGlyphs + 1;
    })
  },

  5: {
    imageSize: r.uint32,
    bigMetrics: BigMetrics,
    numGlyphs: r.uint32,
    glyphCodeArray: new r.Array(r.uint16, 'numGlyphs')
  }
});

var IndexSubtableArray = new r.Struct({
  firstGlyphIndex: r.uint16,
  lastGlyphIndex: r.uint16,
  subtable: new r.Pointer(r.uint32, IndexSubtable)
});

var BitmapSizeTable = new r.Struct({
  indexSubTableArray: new r.Pointer(r.uint32, new r.Array(IndexSubtableArray, 1), { type: 'parent' }),
  indexTablesSize: r.uint32,
  numberOfIndexSubTables: r.uint32,
  colorRef: r.uint32,
  hori: SBitLineMetrics,
  vert: SBitLineMetrics,
  startGlyphIndex: r.uint16,
  endGlyphIndex: r.uint16,
  ppemX: r.uint8,
  ppemY: r.uint8,
  bitDepth: r.uint8,
  flags: new r.Bitfield(r.uint8, ['horizontal', 'vertical'])
});

var EBLC = new r.Struct({
  version: r.uint32, // 0x00020000
  numSizes: r.uint32,
  sizes: new r.Array(BitmapSizeTable, 'numSizes')
});

var ImageTable = new r.Struct({
  ppem: r.uint16,
  resolution: r.uint16,
  imageOffsets: new r.Array(new r.Pointer(r.uint32, 'void'), function (t) {
    return t.parent.parent.maxp.numGlyphs + 1;
  })
});

// This is the Apple sbix table, used by the "Apple Color Emoji" font.
// It includes several image tables with images for each bitmap glyph
// of several different sizes.
var sbix = new r.Struct({
  version: r.uint16,
  flags: new r.Bitfield(r.uint16, ['renderOutlines']),
  numImgTables: r.uint32,
  imageTables: new r.Array(new r.Pointer(r.uint32, ImageTable), 'numImgTables')
});

var LayerRecord = new r.Struct({
  gid: r.uint16, // Glyph ID of layer glyph (must be in z-order from bottom to top).
  paletteIndex: r.uint16 // Index value to use in the appropriate palette. This value must
}); // be less than numPaletteEntries in the CPAL table, except for
// the special case noted below. Each palette entry is 16 bits.
// A palette index of 0xFFFF is a special case indicating that
// the text foreground color should be used.

var BaseGlyphRecord = new r.Struct({
  gid: r.uint16, // Glyph ID of reference glyph. This glyph is for reference only
  // and is not rendered for color.
  firstLayerIndex: r.uint16, // Index (from beginning of the Layer Records) to the layer record.
  // There will be numLayers consecutive entries for this base glyph.
  numLayers: r.uint16
});

var COLR = new r.Struct({
  version: r.uint16,
  numBaseGlyphRecords: r.uint16,
  baseGlyphRecord: new r.Pointer(r.uint32, new r.Array(BaseGlyphRecord, 'numBaseGlyphRecords')),
  layerRecords: new r.Pointer(r.uint32, new r.Array(LayerRecord, 'numLayerRecords'), { lazy: true }),
  numLayerRecords: r.uint16
});

var ColorRecord = new r.Struct({
  blue: r.uint8,
  green: r.uint8,
  red: r.uint8,
  alpha: r.uint8
});

var CPAL = new r.VersionedStruct(r.uint16, {
  header: {
    numPaletteEntries: r.uint16,
    numPalettes: r.uint16,
    numColorRecords: r.uint16,
    colorRecords: new r.Pointer(r.uint32, new r.Array(ColorRecord, 'numColorRecords')),
    colorRecordIndices: new r.Array(r.uint16, 'numPalettes')
  },
  0: {},
  1: {
    offsetPaletteTypeArray: new r.Pointer(r.uint32, new r.Array(r.uint32, 'numPalettes')),
    offsetPaletteLabelArray: new r.Pointer(r.uint32, new r.Array(r.uint16, 'numPalettes')),
    offsetPaletteEntryLabelArray: new r.Pointer(r.uint32, new r.Array(r.uint16, 'numPaletteEntries'))
  }
});

var BaseCoord = new r.VersionedStruct(r.uint16, {
  1: { // Design units only
    coordinate: r.int16 // X or Y value, in design units
  },

  2: { // Design units plus contour point
    coordinate: r.int16, // X or Y value, in design units
    referenceGlyph: r.uint16, // GlyphID of control glyph
    baseCoordPoint: r.uint16 // Index of contour point on the referenceGlyph
  },

  3: { // Design units plus Device table
    coordinate: r.int16, // X or Y value, in design units
    deviceTable: new r.Pointer(r.uint16, Device) // Device table for X or Y value
  }
});

var BaseValues = new r.Struct({
  defaultIndex: r.uint16, // Index of default baseline for this script-same index in the BaseTagList
  baseCoordCount: r.uint16,
  baseCoords: new r.Array(new r.Pointer(r.uint16, BaseCoord), 'baseCoordCount')
});

var FeatMinMaxRecord = new r.Struct({
  tag: new r.String(4), // 4-byte feature identification tag-must match FeatureTag in FeatureList
  minCoord: new r.Pointer(r.uint16, BaseCoord, { type: 'parent' }), // May be NULL
  maxCoord: new r.Pointer(r.uint16, BaseCoord, { type: 'parent' }) // May be NULL
});

var MinMax = new r.Struct({
  minCoord: new r.Pointer(r.uint16, BaseCoord), // May be NULL
  maxCoord: new r.Pointer(r.uint16, BaseCoord), // May be NULL
  featMinMaxCount: r.uint16, // May be 0
  featMinMaxRecords: new r.Array(FeatMinMaxRecord, 'featMinMaxCount') // In alphabetical order
});

var BaseLangSysRecord = new r.Struct({
  tag: new r.String(4), // 4-byte language system identification tag
  minMax: new r.Pointer(r.uint16, MinMax, { type: 'parent' })
});

var BaseScript = new r.Struct({
  baseValues: new r.Pointer(r.uint16, BaseValues), // May be NULL
  defaultMinMax: new r.Pointer(r.uint16, MinMax), // May be NULL
  baseLangSysCount: r.uint16, // May be 0
  baseLangSysRecords: new r.Array(BaseLangSysRecord, 'baseLangSysCount') // in alphabetical order by BaseLangSysTag
});

var BaseScriptRecord = new r.Struct({
  tag: new r.String(4), // 4-byte script identification tag
  script: new r.Pointer(r.uint16, BaseScript, { type: 'parent' })
});

var BaseScriptList = new r.Array(BaseScriptRecord, r.uint16);

// Array of 4-byte baseline identification tags-must be in alphabetical order
var BaseTagList = new r.Array(new r.String(4), r.uint16);

var Axis = new r.Struct({
  baseTagList: new r.Pointer(r.uint16, BaseTagList), // May be NULL
  baseScriptList: new r.Pointer(r.uint16, BaseScriptList)
});

var BASE = new r.VersionedStruct(r.uint32, {
  header: {
    horizAxis: new r.Pointer(r.uint16, Axis), // May be NULL
    vertAxis: new r.Pointer(r.uint16, Axis) // May be NULL
  },

  0x00010000: {},
  0x00010001: {
    itemVariationStore: new r.Pointer(r.uint32, ItemVariationStore)
  }
});

var AttachPoint = new r.Array(r.uint16, r.uint16);
var AttachList = new r.Struct({
  coverage: new r.Pointer(r.uint16, Coverage),
  glyphCount: r.uint16,
  attachPoints: new r.Array(new r.Pointer(r.uint16, AttachPoint), 'glyphCount')
});

var CaretValue = new r.VersionedStruct(r.uint16, {
  1: { // Design units only
    coordinate: r.int16
  },

  2: { // Contour point
    caretValuePoint: r.uint16
  },

  3: { // Design units plus Device table
    coordinate: r.int16,
    deviceTable: new r.Pointer(r.uint16, Device)
  }
});

var LigGlyph = new r.Array(new r.Pointer(r.uint16, CaretValue), r.uint16);

var LigCaretList = new r.Struct({
  coverage: new r.Pointer(r.uint16, Coverage),
  ligGlyphCount: r.uint16,
  ligGlyphs: new r.Array(new r.Pointer(r.uint16, LigGlyph), 'ligGlyphCount')
});

var MarkGlyphSetsDef = new r.Struct({
  markSetTableFormat: r.uint16,
  markSetCount: r.uint16,
  coverage: new r.Array(new r.Pointer(r.uint32, Coverage), 'markSetCount')
});

var GDEF = new r.VersionedStruct(r.uint32, {
  header: {
    glyphClassDef: new r.Pointer(r.uint16, ClassDef),
    attachList: new r.Pointer(r.uint16, AttachList),
    ligCaretList: new r.Pointer(r.uint16, LigCaretList),
    markAttachClassDef: new r.Pointer(r.uint16, ClassDef)
  },

  0x00010000: {},
  0x00010002: {
    markGlyphSetsDef: new r.Pointer(r.uint16, MarkGlyphSetsDef)
  },
  0x00010003: {
    markGlyphSetsDef: new r.Pointer(r.uint16, MarkGlyphSetsDef),
    itemVariationStore: new r.Pointer(r.uint32, ItemVariationStore)
  }
});

var ValueFormat = new r.Bitfield(r.uint16, ['xPlacement', 'yPlacement', 'xAdvance', 'yAdvance', 'xPlaDevice', 'yPlaDevice', 'xAdvDevice', 'yAdvDevice']);

var types = {
  xPlacement: r.int16,
  yPlacement: r.int16,
  xAdvance: r.int16,
  yAdvance: r.int16,
  xPlaDevice: new r.Pointer(r.uint16, Device, { type: 'global', relativeTo: 'rel' }),
  yPlaDevice: new r.Pointer(r.uint16, Device, { type: 'global', relativeTo: 'rel' }),
  xAdvDevice: new r.Pointer(r.uint16, Device, { type: 'global', relativeTo: 'rel' }),
  yAdvDevice: new r.Pointer(r.uint16, Device, { type: 'global', relativeTo: 'rel' })
};

var ValueRecord = function () {
  function ValueRecord() {
    var key = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'valueFormat';

    _classCallCheck(this, ValueRecord);

    this.key = key;
  }

  ValueRecord.prototype.buildStruct = function buildStruct(parent) {
    var struct = parent;
    while (!struct[this.key] && struct.parent) {
      struct = struct.parent;
    }

    if (!struct[this.key]) return;

    var fields = {};
    fields.rel = function () {
      return struct._startOffset;
    };

    var format = struct[this.key];
    for (var key in format) {
      if (format[key]) {
        fields[key] = types[key];
      }
    }

    return new r.Struct(fields);
  };

  ValueRecord.prototype.size = function size(val, ctx) {
    return this.buildStruct(ctx).size(val, ctx);
  };

  ValueRecord.prototype.decode = function decode(stream, parent) {
    var res = this.buildStruct(parent).decode(stream, parent);
    delete res.rel;
    return res;
  };

  return ValueRecord;
}();

var PairValueRecord = new r.Struct({
  secondGlyph: r.uint16,
  value1: new ValueRecord('valueFormat1'),
  value2: new ValueRecord('valueFormat2')
});

var PairSet = new r.Array(PairValueRecord, r.uint16);

var Class2Record = new r.Struct({
  value1: new ValueRecord('valueFormat1'),
  value2: new ValueRecord('valueFormat2')
});

var Anchor = new r.VersionedStruct(r.uint16, {
  1: { // Design units only
    xCoordinate: r.int16,
    yCoordinate: r.int16
  },

  2: { // Design units plus contour point
    xCoordinate: r.int16,
    yCoordinate: r.int16,
    anchorPoint: r.uint16
  },

  3: { // Design units plus Device tables
    xCoordinate: r.int16,
    yCoordinate: r.int16,
    xDeviceTable: new r.Pointer(r.uint16, Device),
    yDeviceTable: new r.Pointer(r.uint16, Device)
  }
});

var EntryExitRecord = new r.Struct({
  entryAnchor: new r.Pointer(r.uint16, Anchor, { type: 'parent' }),
  exitAnchor: new r.Pointer(r.uint16, Anchor, { type: 'parent' })
});

var MarkRecord = new r.Struct({
  class: r.uint16,
  markAnchor: new r.Pointer(r.uint16, Anchor, { type: 'parent' })
});

var MarkArray = new r.Array(MarkRecord, r.uint16);

var BaseRecord = new r.Array(new r.Pointer(r.uint16, Anchor), function (t) {
  return t.parent.classCount;
});
var BaseArray = new r.Array(BaseRecord, r.uint16);

var ComponentRecord = new r.Array(new r.Pointer(r.uint16, Anchor), function (t) {
  return t.parent.parent.classCount;
});
var LigatureAttach = new r.Array(ComponentRecord, r.uint16);
var LigatureArray = new r.Array(new r.Pointer(r.uint16, LigatureAttach), r.uint16);

var GPOSLookup = new r.VersionedStruct('lookupType', {
  1: new r.VersionedStruct(r.uint16, { // Single Adjustment
    1: { // Single positioning value
      coverage: new r.Pointer(r.uint16, Coverage),
      valueFormat: ValueFormat,
      value: new ValueRecord()
    },
    2: {
      coverage: new r.Pointer(r.uint16, Coverage),
      valueFormat: ValueFormat,
      valueCount: r.uint16,
      values: new r.LazyArray(new ValueRecord(), 'valueCount')
    }
  }),

  2: new r.VersionedStruct(r.uint16, { // Pair Adjustment Positioning
    1: { // Adjustments for glyph pairs
      coverage: new r.Pointer(r.uint16, Coverage),
      valueFormat1: ValueFormat,
      valueFormat2: ValueFormat,
      pairSetCount: r.uint16,
      pairSets: new r.LazyArray(new r.Pointer(r.uint16, PairSet), 'pairSetCount')
    },

    2: { // Class pair adjustment
      coverage: new r.Pointer(r.uint16, Coverage),
      valueFormat1: ValueFormat,
      valueFormat2: ValueFormat,
      classDef1: new r.Pointer(r.uint16, ClassDef),
      classDef2: new r.Pointer(r.uint16, ClassDef),
      class1Count: r.uint16,
      class2Count: r.uint16,
      classRecords: new r.LazyArray(new r.LazyArray(Class2Record, 'class2Count'), 'class1Count')
    }
  }),

  3: { // Cursive Attachment Positioning
    format: r.uint16,
    coverage: new r.Pointer(r.uint16, Coverage),
    entryExitCount: r.uint16,
    entryExitRecords: new r.Array(EntryExitRecord, 'entryExitCount')
  },

  4: { // MarkToBase Attachment Positioning
    format: r.uint16,
    markCoverage: new r.Pointer(r.uint16, Coverage),
    baseCoverage: new r.Pointer(r.uint16, Coverage),
    classCount: r.uint16,
    markArray: new r.Pointer(r.uint16, MarkArray),
    baseArray: new r.Pointer(r.uint16, BaseArray)
  },

  5: { // MarkToLigature Attachment Positioning
    format: r.uint16,
    markCoverage: new r.Pointer(r.uint16, Coverage),
    ligatureCoverage: new r.Pointer(r.uint16, Coverage),
    classCount: r.uint16,
    markArray: new r.Pointer(r.uint16, MarkArray),
    ligatureArray: new r.Pointer(r.uint16, LigatureArray)
  },

  6: { // MarkToMark Attachment Positioning
    format: r.uint16,
    mark1Coverage: new r.Pointer(r.uint16, Coverage),
    mark2Coverage: new r.Pointer(r.uint16, Coverage),
    classCount: r.uint16,
    mark1Array: new r.Pointer(r.uint16, MarkArray),
    mark2Array: new r.Pointer(r.uint16, BaseArray)
  },

  7: Context, // Contextual positioning
  8: ChainingContext, // Chaining contextual positioning

  9: { // Extension Positioning
    posFormat: r.uint16,
    lookupType: r.uint16, // cannot also be 9
    extension: new r.Pointer(r.uint32, GPOSLookup)
  }
});

// Fix circular reference
GPOSLookup.versions[9].extension.type = GPOSLookup;

var GPOS = new r.VersionedStruct(r.uint32, {
  header: {
    scriptList: new r.Pointer(r.uint16, ScriptList),
    featureList: new r.Pointer(r.uint16, FeatureList),
    lookupList: new r.Pointer(r.uint16, new LookupList(GPOSLookup))
  },

  0x00010000: {},
  0x00010001: {
    featureVariations: new r.Pointer(r.uint32, FeatureVariations)
  }
});

var Sequence = new r.Array(r.uint16, r.uint16);
var AlternateSet = Sequence;

var Ligature = new r.Struct({
  glyph: r.uint16,
  compCount: r.uint16,
  components: new r.Array(r.uint16, function (t) {
    return t.compCount - 1;
  })
});

var LigatureSet = new r.Array(new r.Pointer(r.uint16, Ligature), r.uint16);

var GSUBLookup = new r.VersionedStruct('lookupType', {
  1: new r.VersionedStruct(r.uint16, { // Single Substitution
    1: {
      coverage: new r.Pointer(r.uint16, Coverage),
      deltaGlyphID: r.int16
    },
    2: {
      coverage: new r.Pointer(r.uint16, Coverage),
      glyphCount: r.uint16,
      substitute: new r.LazyArray(r.uint16, 'glyphCount')
    }
  }),

  2: { // Multiple Substitution
    substFormat: r.uint16,
    coverage: new r.Pointer(r.uint16, Coverage),
    count: r.uint16,
    sequences: new r.LazyArray(new r.Pointer(r.uint16, Sequence), 'count')
  },

  3: { // Alternate Substitution
    substFormat: r.uint16,
    coverage: new r.Pointer(r.uint16, Coverage),
    count: r.uint16,
    alternateSet: new r.LazyArray(new r.Pointer(r.uint16, AlternateSet), 'count')
  },

  4: { // Ligature Substitution
    substFormat: r.uint16,
    coverage: new r.Pointer(r.uint16, Coverage),
    count: r.uint16,
    ligatureSets: new r.LazyArray(new r.Pointer(r.uint16, LigatureSet), 'count')
  },

  5: Context, // Contextual Substitution
  6: ChainingContext, // Chaining Contextual Substitution

  7: { // Extension Substitution
    substFormat: r.uint16,
    lookupType: r.uint16, // cannot also be 7
    extension: new r.Pointer(r.uint32, GSUBLookup)
  },

  8: { // Reverse Chaining Contextual Single Substitution
    substFormat: r.uint16,
    coverage: new r.Pointer(r.uint16, Coverage),
    backtrackCoverage: new r.Array(new r.Pointer(r.uint16, Coverage), 'backtrackGlyphCount'),
    lookaheadGlyphCount: r.uint16,
    lookaheadCoverage: new r.Array(new r.Pointer(r.uint16, Coverage), 'lookaheadGlyphCount'),
    glyphCount: r.uint16,
    substitutes: new r.Array(r.uint16, 'glyphCount')
  }
});

// Fix circular reference
GSUBLookup.versions[7].extension.type = GSUBLookup;

var GSUB = new r.VersionedStruct(r.uint32, {
  header: {
    scriptList: new r.Pointer(r.uint16, ScriptList),
    featureList: new r.Pointer(r.uint16, FeatureList),
    lookupList: new r.Pointer(r.uint16, new LookupList(GSUBLookup))
  },

  0x00010000: {},
  0x00010001: {
    featureVariations: new r.Pointer(r.uint32, FeatureVariations)
  }
});

var JstfGSUBModList = new r.Array(r.uint16, r.uint16);

var JstfPriority = new r.Struct({
  shrinkageEnableGSUB: new r.Pointer(r.uint16, JstfGSUBModList),
  shrinkageDisableGSUB: new r.Pointer(r.uint16, JstfGSUBModList),
  shrinkageEnableGPOS: new r.Pointer(r.uint16, JstfGSUBModList),
  shrinkageDisableGPOS: new r.Pointer(r.uint16, JstfGSUBModList),
  shrinkageJstfMax: new r.Pointer(r.uint16, new LookupList(GPOSLookup)),
  extensionEnableGSUB: new r.Pointer(r.uint16, JstfGSUBModList),
  extensionDisableGSUB: new r.Pointer(r.uint16, JstfGSUBModList),
  extensionEnableGPOS: new r.Pointer(r.uint16, JstfGSUBModList),
  extensionDisableGPOS: new r.Pointer(r.uint16, JstfGSUBModList),
  extensionJstfMax: new r.Pointer(r.uint16, new LookupList(GPOSLookup))
});

var JstfLangSys = new r.Array(new r.Pointer(r.uint16, JstfPriority), r.uint16);

var JstfLangSysRecord = new r.Struct({
  tag: new r.String(4),
  jstfLangSys: new r.Pointer(r.uint16, JstfLangSys)
});

var JstfScript = new r.Struct({
  extenderGlyphs: new r.Pointer(r.uint16, new r.Array(r.uint16, r.uint16)), // array of glyphs to extend line length
  defaultLangSys: new r.Pointer(r.uint16, JstfLangSys),
  langSysCount: r.uint16,
  langSysRecords: new r.Array(JstfLangSysRecord, 'langSysCount')
});

var JstfScriptRecord = new r.Struct({
  tag: new r.String(4),
  script: new r.Pointer(r.uint16, JstfScript, { type: 'parent' })
});

var JSTF = new r.Struct({
  version: r.uint32, // should be 0x00010000
  scriptCount: r.uint16,
  scriptList: new r.Array(JstfScriptRecord, 'scriptCount')
});

// TODO: add this to restructure

var VariableSizeNumber = function () {
  function VariableSizeNumber(size) {
    _classCallCheck(this, VariableSizeNumber);

    this._size = size;
  }

  VariableSizeNumber.prototype.decode = function decode(stream, parent) {
    switch (this.size(0, parent)) {
      case 1:
        return stream.readUInt8();
      case 2:
        return stream.readUInt16BE();
      case 3:
        return stream.readUInt24BE();
      case 4:
        return stream.readUInt32BE();
    }
  };

  VariableSizeNumber.prototype.size = function size(val, parent) {
    return restructure_src_utils.resolveLength(this._size, null, parent);
  };

  return VariableSizeNumber;
}();

var MapDataEntry = new r.Struct({
  entry: new VariableSizeNumber(function (t) {
    return ((t.parent.entryFormat & 0x0030) >> 4) + 1;
  }),
  outerIndex: function outerIndex(t) {
    return t.entry >> (t.parent.entryFormat & 0x000F) + 1;
  },
  innerIndex: function innerIndex(t) {
    return t.entry & (1 << (t.parent.entryFormat & 0x000F) + 1) - 1;
  }
});

var DeltaSetIndexMap = new r.Struct({
  entryFormat: r.uint16,
  mapCount: r.uint16,
  mapData: new r.Array(MapDataEntry, 'mapCount')
});

var HVAR = new r.Struct({
  majorVersion: r.uint16,
  minorVersion: r.uint16,
  itemVariationStore: new r.Pointer(r.uint32, ItemVariationStore),
  advanceWidthMapping: new r.Pointer(r.uint32, DeltaSetIndexMap),
  LSBMapping: new r.Pointer(r.uint32, DeltaSetIndexMap),
  RSBMapping: new r.Pointer(r.uint32, DeltaSetIndexMap)
});

var Signature = new r.Struct({
  format: r.uint32,
  length: r.uint32,
  offset: r.uint32
});

var SignatureBlock = new r.Struct({
  reserved: new r.Reserved(r.uint16, 2),
  cbSignature: r.uint32, // Length (in bytes) of the PKCS#7 packet in pbSignature
  signature: new r.Buffer('cbSignature')
});

var DSIG = new r.Struct({
  ulVersion: r.uint32, // Version number of the DSIG table (0x00000001)
  usNumSigs: r.uint16, // Number of signatures in the table
  usFlag: r.uint16, // Permission flags
  signatures: new r.Array(Signature, 'usNumSigs'),
  signatureBlocks: new r.Array(SignatureBlock, 'usNumSigs')
});

var GaspRange = new r.Struct({
  rangeMaxPPEM: r.uint16, // Upper limit of range, in ppem
  rangeGaspBehavior: new r.Bitfield(r.uint16, [// Flags describing desired rasterizer behavior
  'grayscale', 'gridfit', 'symmetricSmoothing', 'symmetricGridfit' // only in version 1, for ClearType
  ])
});

var gasp = new r.Struct({
  version: r.uint16, // set to 0
  numRanges: r.uint16,
  gaspRanges: new r.Array(GaspRange, 'numRanges') // Sorted by ppem
});

var DeviceRecord = new r.Struct({
  pixelSize: r.uint8,
  maximumWidth: r.uint8,
  widths: new r.Array(r.uint8, function (t) {
    return t.parent.parent.maxp.numGlyphs;
  })
});

// The Horizontal Device Metrics table stores integer advance widths scaled to particular pixel sizes
var hdmx = new r.Struct({
  version: r.uint16,
  numRecords: r.int16,
  sizeDeviceRecord: r.int32,
  records: new r.Array(DeviceRecord, 'numRecords')
});

var KernPair = new r.Struct({
  left: r.uint16,
  right: r.uint16,
  value: r.int16
});

var ClassTable = new r.Struct({
  firstGlyph: r.uint16,
  nGlyphs: r.uint16,
  offsets: new r.Array(r.uint16, 'nGlyphs'),
  max: function max(t) {
    return t.offsets.length && Math.max.apply(Math, t.offsets);
  }
});

var Kern2Array = new r.Struct({
  off: function off(t) {
    return t._startOffset - t.parent.parent._startOffset;
  },
  len: function len(t) {
    return ((t.parent.leftTable.max - t.off) / t.parent.rowWidth + 1) * (t.parent.rowWidth / 2);
  },
  values: new r.LazyArray(r.int16, 'len')
});

var KernSubtable = new r.VersionedStruct('format', {
  0: {
    nPairs: r.uint16,
    searchRange: r.uint16,
    entrySelector: r.uint16,
    rangeShift: r.uint16,
    pairs: new r.Array(KernPair, 'nPairs')
  },

  2: {
    rowWidth: r.uint16,
    leftTable: new r.Pointer(r.uint16, ClassTable, { type: 'parent' }),
    rightTable: new r.Pointer(r.uint16, ClassTable, { type: 'parent' }),
    array: new r.Pointer(r.uint16, Kern2Array, { type: 'parent' })
  },

  3: {
    glyphCount: r.uint16,
    kernValueCount: r.uint8,
    leftClassCount: r.uint8,
    rightClassCount: r.uint8,
    flags: r.uint8,
    kernValue: new r.Array(r.int16, 'kernValueCount'),
    leftClass: new r.Array(r.uint8, 'glyphCount'),
    rightClass: new r.Array(r.uint8, 'glyphCount'),
    kernIndex: new r.Array(r.uint8, function (t) {
      return t.leftClassCount * t.rightClassCount;
    })
  }
});

var KernTable = new r.VersionedStruct('version', {
  0: { // Microsoft uses this format
    subVersion: r.uint16, // Microsoft has an extra sub-table version number
    length: r.uint16, // Length of the subtable, in bytes
    format: r.uint8, // Format of subtable
    coverage: new r.Bitfield(r.uint8, ['horizontal', // 1 if table has horizontal data, 0 if vertical
    'minimum', // If set to 1, the table has minimum values. If set to 0, the table has kerning values.
    'crossStream', // If set to 1, kerning is perpendicular to the flow of the text
    'override' // If set to 1 the value in this table replaces the accumulated value
    ]),
    subtable: KernSubtable,
    padding: new r.Reserved(r.uint8, function (t) {
      return t.length - t._currentOffset;
    })
  },
  1: { // Apple uses this format
    length: r.uint32,
    coverage: new r.Bitfield(r.uint8, [null, null, null, null, null, 'variation', // Set if table has variation kerning values
    'crossStream', // Set if table has cross-stream kerning values
    'vertical' // Set if table has vertical kerning values
    ]),
    format: r.uint8,
    tupleIndex: r.uint16,
    subtable: KernSubtable,
    padding: new r.Reserved(r.uint8, function (t) {
      return t.length - t._currentOffset;
    })
  }
});

var kern = new r.VersionedStruct(r.uint16, {
  0: { // Microsoft Version
    nTables: r.uint16,
    tables: new r.Array(KernTable, 'nTables')
  },

  1: { // Apple Version
    reserved: new r.Reserved(r.uint16), // the other half of the version number
    nTables: r.uint32,
    tables: new r.Array(KernTable, 'nTables')
  }
});

// Linear Threshold table
// Records the ppem for each glyph at which the scaling becomes linear again,
// despite instructions effecting the advance width
var LTSH = new r.Struct({
  version: r.uint16,
  numGlyphs: r.uint16,
  yPels: new r.Array(r.uint8, 'numGlyphs')
});

// PCL 5 Table
// NOTE: The PCLT table is strongly discouraged for OpenType fonts with TrueType outlines
var PCLT = new r.Struct({
  version: r.uint16,
  fontNumber: r.uint32,
  pitch: r.uint16,
  xHeight: r.uint16,
  style: r.uint16,
  typeFamily: r.uint16,
  capHeight: r.uint16,
  symbolSet: r.uint16,
  typeface: new r.String(16),
  characterComplement: new r.String(8),
  fileName: new r.String(6),
  strokeWeight: new r.String(1),
  widthType: new r.String(1),
  serifStyle: r.uint8,
  reserved: new r.Reserved(r.uint8)
});

// VDMX tables contain ascender/descender overrides for certain (usually small)
// sizes. This is needed in order to match font metrics on Windows.

var Ratio = new r.Struct({
  bCharSet: r.uint8, // Character set
  xRatio: r.uint8, // Value to use for x-Ratio
  yStartRatio: r.uint8, // Starting y-Ratio value
  yEndRatio: r.uint8 // Ending y-Ratio value
});

var vTable = new r.Struct({
  yPelHeight: r.uint16, // yPelHeight to which values apply
  yMax: r.int16, // Maximum value (in pels) for this yPelHeight
  yMin: r.int16 // Minimum value (in pels) for this yPelHeight
});

var VdmxGroup = new r.Struct({
  recs: r.uint16, // Number of height records in this group
  startsz: r.uint8, // Starting yPelHeight
  endsz: r.uint8, // Ending yPelHeight
  entries: new r.Array(vTable, 'recs') // The VDMX records
});

var VDMX = new r.Struct({
  version: r.uint16, // Version number (0 or 1)
  numRecs: r.uint16, // Number of VDMX groups present
  numRatios: r.uint16, // Number of aspect ratio groupings
  ratioRanges: new r.Array(Ratio, 'numRatios'), // Ratio ranges
  offsets: new r.Array(r.uint16, 'numRatios'), // Offset to the VDMX group for this ratio range
  groups: new r.Array(VdmxGroup, 'numRecs') // The actual VDMX groupings
});

// Vertical Header Table
var vhea = new r.Struct({
  version: r.uint16, // Version number of the Vertical Header Table
  ascent: r.int16, // The vertical typographic ascender for this font
  descent: r.int16, // The vertical typographic descender for this font
  lineGap: r.int16, // The vertical typographic line gap for this font
  advanceHeightMax: r.int16, // The maximum advance height measurement found in the font
  minTopSideBearing: r.int16, // The minimum top side bearing measurement found in the font
  minBottomSideBearing: r.int16, // The minimum bottom side bearing measurement found in the font
  yMaxExtent: r.int16,
  caretSlopeRise: r.int16, // Caret slope (rise/run)
  caretSlopeRun: r.int16,
  caretOffset: r.int16, // Set value equal to 0 for nonslanted fonts
  reserved: new r.Reserved(r.int16, 4),
  metricDataFormat: r.int16, // Set to 0
  numberOfMetrics: r.uint16 // Number of advance heights in the Vertical Metrics table
});

var VmtxEntry = new r.Struct({
  advance: r.uint16, // The advance height of the glyph
  bearing: r.int16 // The top sidebearing of the glyph
});

// Vertical Metrics Table
var vmtx = new r.Struct({
  metrics: new r.LazyArray(VmtxEntry, function (t) {
    return t.parent.vhea.numberOfMetrics;
  }),
  bearings: new r.LazyArray(r.int16, function (t) {
    return t.parent.maxp.numGlyphs - t.parent.vhea.numberOfMetrics;
  })
});

var shortFrac = new r.Fixed(16, 'BE', 14);

var Correspondence = new r.Struct({
  fromCoord: shortFrac,
  toCoord: shortFrac
});

var Segment = new r.Struct({
  pairCount: r.uint16,
  correspondence: new r.Array(Correspondence, 'pairCount')
});

var avar = new r.Struct({
  version: r.fixed32,
  axisCount: r.uint32,
  segment: new r.Array(Segment, 'axisCount')
});

var UnboundedArrayAccessor = function () {
  function UnboundedArrayAccessor(type, stream, parent) {
    _classCallCheck(this, UnboundedArrayAccessor);

    this.type = type;
    this.stream = stream;
    this.parent = parent;
    this.base = this.stream.pos;
    this._items = [];
  }

  UnboundedArrayAccessor.prototype.getItem = function getItem(index) {
    if (this._items[index] == null) {
      var pos = this.stream.pos;
      this.stream.pos = this.base + this.type.size(null, this.parent) * index;
      this._items[index] = this.type.decode(this.stream, this.parent);
      this.stream.pos = pos;
    }

    return this._items[index];
  };

  UnboundedArrayAccessor.prototype.inspect = function inspect() {
    return '[UnboundedArray ' + this.type.constructor.name + ']';
  };

  return UnboundedArrayAccessor;
}();

var UnboundedArray = function (_r$Array) {
  _inherits(UnboundedArray, _r$Array);

  function UnboundedArray(type) {
    _classCallCheck(this, UnboundedArray);

    return _possibleConstructorReturn(this, _r$Array.call(this, type, 0));
  }

  UnboundedArray.prototype.decode = function decode(stream, parent) {
    return new UnboundedArrayAccessor(this.type, stream, parent);
  };

  return UnboundedArray;
}(r.Array);

var LookupTable = function LookupTable() {
  var ValueType = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : r.uint16;

  // Helper class that makes internal structures invisible to pointers
  var Shadow = function () {
    function Shadow(type) {
      _classCallCheck(this, Shadow);

      this.type = type;
    }

    Shadow.prototype.decode = function decode(stream, ctx) {
      ctx = ctx.parent.parent;
      return this.type.decode(stream, ctx);
    };

    Shadow.prototype.size = function size(val, ctx) {
      ctx = ctx.parent.parent;
      return this.type.size(val, ctx);
    };

    Shadow.prototype.encode = function encode(stream, val, ctx) {
      ctx = ctx.parent.parent;
      return this.type.encode(stream, val, ctx);
    };

    return Shadow;
  }();

  ValueType = new Shadow(ValueType);

  var BinarySearchHeader = new r.Struct({
    unitSize: r.uint16,
    nUnits: r.uint16,
    searchRange: r.uint16,
    entrySelector: r.uint16,
    rangeShift: r.uint16
  });

  var LookupSegmentSingle = new r.Struct({
    lastGlyph: r.uint16,
    firstGlyph: r.uint16,
    value: ValueType
  });

  var LookupSegmentArray = new r.Struct({
    lastGlyph: r.uint16,
    firstGlyph: r.uint16,
    values: new r.Pointer(r.uint16, new r.Array(ValueType, function (t) {
      return t.lastGlyph - t.firstGlyph + 1;
    }), { type: 'parent' })
  });

  var LookupSingle = new r.Struct({
    glyph: r.uint16,
    value: ValueType
  });

  return new r.VersionedStruct(r.uint16, {
    0: {
      values: new UnboundedArray(ValueType) // length == number of glyphs maybe?
    },
    2: {
      binarySearchHeader: BinarySearchHeader,
      segments: new r.Array(LookupSegmentSingle, function (t) {
        return t.binarySearchHeader.nUnits;
      })
    },
    4: {
      binarySearchHeader: BinarySearchHeader,
      segments: new r.Array(LookupSegmentArray, function (t) {
        return t.binarySearchHeader.nUnits;
      })
    },
    6: {
      binarySearchHeader: BinarySearchHeader,
      segments: new r.Array(LookupSingle, function (t) {
        return t.binarySearchHeader.nUnits;
      })
    },
    8: {
      firstGlyph: r.uint16,
      count: r.uint16,
      values: new r.Array(ValueType, 'count')
    }
  });
};

function StateTable() {
  var entryData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var lookupType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : r.uint16;

  var entry = _Object$assign({
    newState: r.uint16,
    flags: r.uint16
  }, entryData);

  var Entry = new r.Struct(entry);
  var StateArray = new UnboundedArray(new r.Array(r.uint16, function (t) {
    return t.nClasses;
  }));

  var StateHeader = new r.Struct({
    nClasses: r.uint32,
    classTable: new r.Pointer(r.uint32, new LookupTable(lookupType)),
    stateArray: new r.Pointer(r.uint32, StateArray),
    entryTable: new r.Pointer(r.uint32, new UnboundedArray(Entry))
  });

  return StateHeader;
}

// This is the old version of the StateTable structure
function StateTable1() {
  var entryData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var lookupType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : r.uint16;

  var ClassLookupTable = new r.Struct({
    version: function version() {
      return 8;
    },
    // simulate LookupTable
    firstGlyph: r.uint16,
    values: new r.Array(r.uint8, r.uint16)
  });

  var entry = _Object$assign({
    newStateOffset: r.uint16,
    // convert offset to stateArray index
    newState: function newState(t) {
      return (t.newStateOffset - (t.parent.stateArray.base - t.parent._startOffset)) / t.parent.nClasses;
    },
    flags: r.uint16
  }, entryData);

  var Entry = new r.Struct(entry);
  var StateArray = new UnboundedArray(new r.Array(r.uint8, function (t) {
    return t.nClasses;
  }));

  var StateHeader1 = new r.Struct({
    nClasses: r.uint16,
    classTable: new r.Pointer(r.uint16, ClassLookupTable),
    stateArray: new r.Pointer(r.uint16, StateArray),
    entryTable: new r.Pointer(r.uint16, new UnboundedArray(Entry))
  });

  return StateHeader1;
}

var BslnSubtable = new r.VersionedStruct('format', {
  0: { // Distance-based, no mapping
    deltas: new r.Array(r.int16, 32)
  },

  1: { // Distance-based, with mapping
    deltas: new r.Array(r.int16, 32),
    mappingData: new LookupTable(r.uint16)
  },

  2: { // Control point-based, no mapping
    standardGlyph: r.uint16,
    controlPoints: new r.Array(r.uint16, 32)
  },

  3: { // Control point-based, with mapping
    standardGlyph: r.uint16,
    controlPoints: new r.Array(r.uint16, 32),
    mappingData: new LookupTable(r.uint16)
  }
});

var bsln = new r.Struct({
  version: r.fixed32,
  format: r.uint16,
  defaultBaseline: r.uint16,
  subtable: BslnSubtable
});

var Setting = new r.Struct({
  setting: r.uint16,
  nameIndex: r.int16,
  name: function name(t) {
    return t.parent.parent.parent.name.records.fontFeatures[t.nameIndex];
  }
});

var FeatureName = new r.Struct({
  feature: r.uint16,
  nSettings: r.uint16,
  settingTable: new r.Pointer(r.uint32, new r.Array(Setting, 'nSettings'), { type: 'parent' }),
  featureFlags: new r.Bitfield(r.uint8, [null, null, null, null, null, null, 'hasDefault', 'exclusive']),
  defaultSetting: r.uint8,
  nameIndex: r.int16,
  name: function name(t) {
    return t.parent.parent.name.records.fontFeatures[t.nameIndex];
  }
});

var feat = new r.Struct({
  version: r.fixed32,
  featureNameCount: r.uint16,
  reserved1: new r.Reserved(r.uint16),
  reserved2: new r.Reserved(r.uint32),
  featureNames: new r.Array(FeatureName, 'featureNameCount')
});

var Axis$1 = new r.Struct({
  axisTag: new r.String(4),
  minValue: r.fixed32,
  defaultValue: r.fixed32,
  maxValue: r.fixed32,
  flags: r.uint16,
  nameID: r.uint16,
  name: function name(t) {
    return t.parent.parent.name.records.fontFeatures[t.nameID];
  }
});

var Instance = new r.Struct({
  nameID: r.uint16,
  name: function name(t) {
    return t.parent.parent.name.records.fontFeatures[t.nameID];
  },
  flags: r.uint16,
  coord: new r.Array(r.fixed32, function (t) {
    return t.parent.axisCount;
  }),
  postscriptNameID: new r.Optional(r.uint16, function (t) {
    return t.parent.instanceSize - t._currentOffset > 0;
  })
});

var fvar = new r.Struct({
  version: r.fixed32,
  offsetToData: r.uint16,
  countSizePairs: r.uint16,
  axisCount: r.uint16,
  axisSize: r.uint16,
  instanceCount: r.uint16,
  instanceSize: r.uint16,
  axis: new r.Array(Axis$1, 'axisCount'),
  instance: new r.Array(Instance, 'instanceCount')
});

var shortFrac$1 = new r.Fixed(16, 'BE', 14);

var Offset = function () {
  function Offset() {
    _classCallCheck(this, Offset);
  }

  Offset.decode = function decode(stream, parent) {
    // In short format, offsets are multiplied by 2.
    // This doesn't seem to be documented by Apple, but it
    // is implemented this way in Freetype.
    return parent.flags ? stream.readUInt32BE() : stream.readUInt16BE() * 2;
  };

  return Offset;
}();

var gvar = new r.Struct({
  version: r.uint16,
  reserved: new r.Reserved(r.uint16),
  axisCount: r.uint16,
  globalCoordCount: r.uint16,
  globalCoords: new r.Pointer(r.uint32, new r.Array(new r.Array(shortFrac$1, 'axisCount'), 'globalCoordCount')),
  glyphCount: r.uint16,
  flags: r.uint16,
  offsetToData: r.uint32,
  offsets: new r.Array(new r.Pointer(Offset, 'void', { relativeTo: 'offsetToData', allowNull: false }), function (t) {
    return t.glyphCount + 1;
  })
});

var ClassTable$1 = new r.Struct({
  length: r.uint16,
  coverage: r.uint16,
  subFeatureFlags: r.uint32,
  stateTable: new StateTable1()
});

var WidthDeltaRecord = new r.Struct({
  justClass: r.uint32,
  beforeGrowLimit: r.fixed32,
  beforeShrinkLimit: r.fixed32,
  afterGrowLimit: r.fixed32,
  afterShrinkLimit: r.fixed32,
  growFlags: r.uint16,
  shrinkFlags: r.uint16
});

var WidthDeltaCluster = new r.Array(WidthDeltaRecord, r.uint32);

var ActionData = new r.VersionedStruct('actionType', {
  0: { // Decomposition action
    lowerLimit: r.fixed32,
    upperLimit: r.fixed32,
    order: r.uint16,
    glyphs: new r.Array(r.uint16, r.uint16)
  },

  1: { // Unconditional add glyph action
    addGlyph: r.uint16
  },

  2: { // Conditional add glyph action
    substThreshold: r.fixed32,
    addGlyph: r.uint16,
    substGlyph: r.uint16
  },

  3: {}, // Stretch glyph action (no data, not supported by CoreText)

  4: { // Ductile glyph action (not supported by CoreText)
    variationAxis: r.uint32,
    minimumLimit: r.fixed32,
    noStretchValue: r.fixed32,
    maximumLimit: r.fixed32
  },

  5: { // Repeated add glyph action
    flags: r.uint16,
    glyph: r.uint16
  }
});

var Action = new r.Struct({
  actionClass: r.uint16,
  actionType: r.uint16,
  actionLength: r.uint32,
  actionData: ActionData,
  padding: new r.Reserved(r.uint8, function (t) {
    return t.actionLength - t._currentOffset;
  })
});

var PostcompensationAction = new r.Array(Action, r.uint32);
var PostCompensationTable = new r.Struct({
  lookupTable: new LookupTable(new r.Pointer(r.uint16, PostcompensationAction))
});

var JustificationTable = new r.Struct({
  classTable: new r.Pointer(r.uint16, ClassTable$1, { type: 'parent' }),
  wdcOffset: r.uint16,
  postCompensationTable: new r.Pointer(r.uint16, PostCompensationTable, { type: 'parent' }),
  widthDeltaClusters: new LookupTable(new r.Pointer(r.uint16, WidthDeltaCluster, { type: 'parent', relativeTo: 'wdcOffset' }))
});

var just = new r.Struct({
  version: r.uint32,
  format: r.uint16,
  horizontal: new r.Pointer(r.uint16, JustificationTable),
  vertical: new r.Pointer(r.uint16, JustificationTable)
});

var LigatureData = {
  action: r.uint16
};

var ContextualData = {
  markIndex: r.uint16,
  currentIndex: r.uint16
};

var InsertionData = {
  currentInsertIndex: r.uint16,
  markedInsertIndex: r.uint16
};

var SubstitutionTable = new r.Struct({
  items: new UnboundedArray(new r.Pointer(r.uint32, new LookupTable()))
});

var SubtableData = new r.VersionedStruct('type', {
  0: { // Indic Rearrangement Subtable
    stateTable: new StateTable()
  },

  1: { // Contextual Glyph Substitution Subtable
    stateTable: new StateTable(ContextualData),
    substitutionTable: new r.Pointer(r.uint32, SubstitutionTable)
  },

  2: { // Ligature subtable
    stateTable: new StateTable(LigatureData),
    ligatureActions: new r.Pointer(r.uint32, new UnboundedArray(r.uint32)),
    components: new r.Pointer(r.uint32, new UnboundedArray(r.uint16)),
    ligatureList: new r.Pointer(r.uint32, new UnboundedArray(r.uint16))
  },

  4: { // Non-contextual Glyph Substitution Subtable
    lookupTable: new LookupTable()
  },

  5: { // Glyph Insertion Subtable
    stateTable: new StateTable(InsertionData),
    insertionActions: new r.Pointer(r.uint32, new UnboundedArray(r.uint16))
  }
});

var Subtable = new r.Struct({
  length: r.uint32,
  coverage: r.uint24,
  type: r.uint8,
  subFeatureFlags: r.uint32,
  table: SubtableData,
  padding: new r.Reserved(r.uint8, function (t) {
    return t.length - t._currentOffset;
  })
});

var FeatureEntry = new r.Struct({
  featureType: r.uint16,
  featureSetting: r.uint16,
  enableFlags: r.uint32,
  disableFlags: r.uint32
});

var MorxChain = new r.Struct({
  defaultFlags: r.uint32,
  chainLength: r.uint32,
  nFeatureEntries: r.uint32,
  nSubtables: r.uint32,
  features: new r.Array(FeatureEntry, 'nFeatureEntries'),
  subtables: new r.Array(Subtable, 'nSubtables')
});

var morx = new r.Struct({
  version: r.uint16,
  unused: new r.Reserved(r.uint16),
  nChains: r.uint32,
  chains: new r.Array(MorxChain, 'nChains')
});

var OpticalBounds = new r.Struct({
  left: r.int16,
  top: r.int16,
  right: r.int16,
  bottom: r.int16
});

var opbd = new r.Struct({
  version: r.fixed32,
  format: r.uint16,
  lookupTable: new LookupTable(OpticalBounds)
});

var tables = {};
// Required Tables
tables.cmap = cmap;
tables.head = head;
tables.hhea = hhea;
tables.hmtx = hmtx;
tables.maxp = maxp;
tables.name = NameTable;
tables['OS/2'] = OS2;
tables.post = post;

// TrueType Outlines
tables.fpgm = fpgm;
tables.loca = loca;
tables.prep = prep;
tables['cvt '] = cvt;
tables.glyf = glyf;

// PostScript Outlines
tables['CFF '] = CFFFont;
tables['CFF2'] = CFFFont;
tables.VORG = VORG;

// Bitmap Glyphs
tables.EBLC = EBLC;
tables.CBLC = tables.EBLC;
tables.sbix = sbix;
tables.COLR = COLR;
tables.CPAL = CPAL;

// Advanced OpenType Tables
tables.BASE = BASE;
tables.GDEF = GDEF;
tables.GPOS = GPOS;
tables.GSUB = GSUB;
tables.JSTF = JSTF;

// OpenType variations tables
tables.HVAR = HVAR;

// Other OpenType Tables
tables.DSIG = DSIG;
tables.gasp = gasp;
tables.hdmx = hdmx;
tables.kern = kern;
tables.LTSH = LTSH;
tables.PCLT = PCLT;
tables.VDMX = VDMX;
tables.vhea = vhea;
tables.vmtx = vmtx;

// Apple Advanced Typography Tables
tables.avar = avar;
tables.bsln = bsln;
tables.feat = feat;
tables.fvar = fvar;
tables.gvar = gvar;
tables.just = just;
tables.morx = morx;
tables.opbd = opbd;

var TableEntry = new r.Struct({
  tag: new r.String(4),
  checkSum: r.uint32,
  offset: new r.Pointer(r.uint32, 'void', { type: 'global' }),
  length: r.uint32
});

var Directory = new r.Struct({
  tag: new r.String(4),
  numTables: r.uint16,
  searchRange: r.uint16,
  entrySelector: r.uint16,
  rangeShift: r.uint16,
  tables: new r.Array(TableEntry, 'numTables')
});

Directory.process = function () {
  var tables = {};
  for (var _iterator = this.tables, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var table = _ref;

    tables[table.tag] = table;
  }

  this.tables = tables;
};

Directory.preEncode = function (stream) {
  var tables$$ = [];
  for (var tag in this.tables) {
    var table = this.tables[tag];
    if (table) {
      tables$$.push({
        tag: tag,
        checkSum: 0,
        offset: new r.VoidPointer(tables[tag], table),
        length: tables[tag].size(table)
      });
    }
  }

  this.tag = 'true';
  this.numTables = tables$$.length;
  this.tables = tables$$;

  var maxExponentFor2 = Math.floor(Math.log(this.numTables) / Math.LN2);
  var maxPowerOf2 = Math.pow(2, maxExponentFor2);

  this.searchRange = maxPowerOf2 * 16;
  this.entrySelector = Math.log(maxPowerOf2) / Math.LN2;
  this.rangeShift = this.numTables * 16 - this.searchRange;
};

function binarySearch(arr, cmp) {
  var min = 0;
  var max = arr.length - 1;
  while (min <= max) {
    var mid = min + max >> 1;
    var res = cmp(arr[mid]);

    if (res < 0) {
      max = mid - 1;
    } else if (res > 0) {
      min = mid + 1;
    } else {
      return mid;
    }
  }

  return -1;
}

function range(index, end) {
  var range = [];
  while (index < end) {
    range.push(index++);
  }
  return range;
}

var _class$1;
function _applyDecoratedDescriptor$1(target, property, decorators, descriptor, context) {
  var desc = {};
  Object['ke' + 'ys'](descriptor).forEach(function (key) {
    desc[key] = descriptor[key];
  });
  desc.enumerable = !!desc.enumerable;
  desc.configurable = !!desc.configurable;

  if ('value' in desc || desc.initializer) {
    desc.writable = true;
  }

  desc = decorators.slice().reverse().reduce(function (desc, decorator) {
    return decorator(target, property, desc) || desc;
  }, desc);

  if (context && desc.initializer !== void 0) {
    desc.value = desc.initializer ? desc.initializer.call(context) : void 0;
    desc.initializer = undefined;
  }

  if (desc.initializer === void 0) {
    Object['define' + 'Property'](target, property, desc);
    desc = null;
  }

  return desc;
}

// iconv-lite is an optional dependency.
try {
  var iconv = __webpack_require__(170);
} catch (err) {}

var CmapProcessor = (_class$1 = function () {
  function CmapProcessor(cmapTable) {
    _classCallCheck(this, CmapProcessor);

    // Attempt to find a Unicode cmap first
    this.encoding = null;
    this.cmap = this.findSubtable(cmapTable, [
    // 32-bit subtables
    [3, 10], [0, 6], [0, 4],

    // 16-bit subtables
    [3, 1], [0, 3], [0, 2], [0, 1], [0, 0]]);

    // If not unicode cmap was found, and iconv-lite is installed,
    // take the first table with a supported encoding.
    if (!this.cmap && iconv) {
      for (var _iterator = cmapTable.tables, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
        var _ref;

        if (_isArray) {
          if (_i >= _iterator.length) break;
          _ref = _iterator[_i++];
        } else {
          _i = _iterator.next();
          if (_i.done) break;
          _ref = _i.value;
        }

        var cmap = _ref;

        var encoding = getEncoding(cmap.platformID, cmap.encodingID, cmap.table.language - 1);
        if (iconv.encodingExists(encoding)) {
          this.cmap = cmap.table;
          this.encoding = encoding;
        }
      }
    }

    if (!this.cmap) {
      throw new Error("Could not find a supported cmap table");
    }

    this.uvs = this.findSubtable(cmapTable, [[0, 5]]);
    if (this.uvs && this.uvs.version !== 14) {
      this.uvs = null;
    }
  }

  CmapProcessor.prototype.findSubtable = function findSubtable(cmapTable, pairs) {
    for (var _iterator2 = pairs, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
      var _ref2;

      if (_isArray2) {
        if (_i2 >= _iterator2.length) break;
        _ref2 = _iterator2[_i2++];
      } else {
        _i2 = _iterator2.next();
        if (_i2.done) break;
        _ref2 = _i2.value;
      }

      var _ref3 = _ref2,
          platformID = _ref3[0],
          encodingID = _ref3[1];

      for (var _iterator3 = cmapTable.tables, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
        var _ref4;

        if (_isArray3) {
          if (_i3 >= _iterator3.length) break;
          _ref4 = _iterator3[_i3++];
        } else {
          _i3 = _iterator3.next();
          if (_i3.done) break;
          _ref4 = _i3.value;
        }

        var cmap = _ref4;

        if (cmap.platformID === platformID && cmap.encodingID === encodingID) {
          return cmap.table;
        }
      }
    }

    return null;
  };

  CmapProcessor.prototype.lookup = function lookup(codepoint, variationSelector) {
    // If there is no Unicode cmap in this font, we need to re-encode
    // the codepoint in the encoding that the cmap supports.
    if (this.encoding) {
      var buf = iconv.encode(_String$fromCodePoint(codepoint), this.encoding);
      codepoint = 0;
      for (var i = 0; i < buf.length; i++) {
        codepoint = codepoint << 8 | buf[i];
      }

      // Otherwise, try to get a Unicode variation selector for this codepoint if one is provided.
    } else if (variationSelector) {
      var gid = this.getVariationSelector(codepoint, variationSelector);
      if (gid) {
        return gid;
      }
    }

    var cmap = this.cmap;
    switch (cmap.version) {
      case 0:
        return cmap.codeMap.get(codepoint) || 0;

      case 4:
        {
          var min = 0;
          var max = cmap.segCount - 1;
          while (min <= max) {
            var mid = min + max >> 1;

            if (codepoint < cmap.startCode.get(mid)) {
              max = mid - 1;
            } else if (codepoint > cmap.endCode.get(mid)) {
              min = mid + 1;
            } else {
              var rangeOffset = cmap.idRangeOffset.get(mid);
              var _gid = void 0;

              if (rangeOffset === 0) {
                _gid = codepoint + cmap.idDelta.get(mid);
              } else {
                var index = rangeOffset / 2 + (codepoint - cmap.startCode.get(mid)) - (cmap.segCount - mid);
                _gid = cmap.glyphIndexArray.get(index) || 0;
                if (_gid !== 0) {
                  _gid += cmap.idDelta.get(mid);
                }
              }

              return _gid & 0xffff;
            }
          }

          return 0;
        }

      case 8:
        throw new Error('TODO: cmap format 8');

      case 6:
      case 10:
        return cmap.glyphIndices.get(codepoint - cmap.firstCode) || 0;

      case 12:
      case 13:
        {
          var _min = 0;
          var _max = cmap.nGroups - 1;
          while (_min <= _max) {
            var _mid = _min + _max >> 1;
            var group = cmap.groups.get(_mid);

            if (codepoint < group.startCharCode) {
              _max = _mid - 1;
            } else if (codepoint > group.endCharCode) {
              _min = _mid + 1;
            } else {
              if (cmap.version === 12) {
                return group.glyphID + (codepoint - group.startCharCode);
              } else {
                return group.glyphID;
              }
            }
          }

          return 0;
        }

      case 14:
        throw new Error('TODO: cmap format 14');

      default:
        throw new Error('Unknown cmap format ' + cmap.version);
    }
  };

  CmapProcessor.prototype.getVariationSelector = function getVariationSelector(codepoint, variationSelector) {
    if (!this.uvs) {
      return 0;
    }

    var selectors = this.uvs.varSelectors.toArray();
    var i = binarySearch(selectors, function (x) {
      return variationSelector - x.varSelector;
    });
    var sel = selectors[i];

    if (i !== -1 && sel.defaultUVS) {
      i = binarySearch(sel.defaultUVS, function (x) {
        return codepoint < x.startUnicodeValue ? -1 : codepoint > x.startUnicodeValue + x.additionalCount ? +1 : 0;
      });
    }

    if (i !== -1 && sel.nonDefaultUVS) {
      i = binarySearch(sel.nonDefaultUVS, function (x) {
        return codepoint - x.unicodeValue;
      });
      if (i !== -1) {
        return sel.nonDefaultUVS[i].glyphID;
      }
    }

    return 0;
  };

  CmapProcessor.prototype.getCharacterSet = function getCharacterSet() {
    var cmap = this.cmap;
    switch (cmap.version) {
      case 0:
        return range(0, cmap.codeMap.length);

      case 4:
        {
          var res = [];
          var endCodes = cmap.endCode.toArray();
          for (var i = 0; i < endCodes.length; i++) {
            var tail = endCodes[i] + 1;
            var start = cmap.startCode.get(i);
            res.push.apply(res, range(start, tail));
          }

          return res;
        }

      case 8:
        throw new Error('TODO: cmap format 8');

      case 6:
      case 10:
        return range(cmap.firstCode, cmap.firstCode + cmap.glyphIndices.length);

      case 12:
      case 13:
        {
          var _res = [];
          for (var _iterator4 = cmap.groups.toArray(), _isArray4 = Array.isArray(_iterator4), _i4 = 0, _iterator4 = _isArray4 ? _iterator4 : _getIterator(_iterator4);;) {
            var _ref5;

            if (_isArray4) {
              if (_i4 >= _iterator4.length) break;
              _ref5 = _iterator4[_i4++];
            } else {
              _i4 = _iterator4.next();
              if (_i4.done) break;
              _ref5 = _i4.value;
            }

            var group = _ref5;

            _res.push.apply(_res, range(group.startCharCode, group.endCharCode + 1));
          }

          return _res;
        }

      case 14:
        throw new Error('TODO: cmap format 14');

      default:
        throw new Error('Unknown cmap format ' + cmap.version);
    }
  };

  CmapProcessor.prototype.codePointsForGlyph = function codePointsForGlyph(gid) {
    var cmap = this.cmap;
    switch (cmap.version) {
      case 0:
        {
          var res = [];
          for (var i = 0; i < 256; i++) {
            if (cmap.codeMap.get(i) === gid) {
              res.push(i);
            }
          }

          return res;
        }

      case 4:
        {
          var _res2 = [];
          for (var _i5 = 0; _i5 < cmap.segCount; _i5++) {
            var end = cmap.endCode.get(_i5);
            var start = cmap.startCode.get(_i5);
            var rangeOffset = cmap.idRangeOffset.get(_i5);
            var delta = cmap.idDelta.get(_i5);

            for (var c = start; c <= end; c++) {
              var g = 0;
              if (rangeOffset === 0) {
                g = c + delta;
              } else {
                var index = rangeOffset / 2 + (c - start) - (cmap.segCount - _i5);
                g = cmap.glyphIndexArray.get(index) || 0;
                if (g !== 0) {
                  g += delta;
                }
              }

              if (g === gid) {
                _res2.push(c);
              }
            }
          }

          return _res2;
        }

      case 12:
        {
          var _res3 = [];
          for (var _iterator5 = cmap.groups.toArray(), _isArray5 = Array.isArray(_iterator5), _i6 = 0, _iterator5 = _isArray5 ? _iterator5 : _getIterator(_iterator5);;) {
            var _ref6;

            if (_isArray5) {
              if (_i6 >= _iterator5.length) break;
              _ref6 = _iterator5[_i6++];
            } else {
              _i6 = _iterator5.next();
              if (_i6.done) break;
              _ref6 = _i6.value;
            }

            var group = _ref6;

            if (gid >= group.glyphID && gid <= group.glyphID + (group.endCharCode - group.startCharCode)) {
              _res3.push(group.startCharCode + (gid - group.glyphID));
            }
          }

          return _res3;
        }

      case 13:
        {
          var _res4 = [];
          for (var _iterator6 = cmap.groups.toArray(), _isArray6 = Array.isArray(_iterator6), _i7 = 0, _iterator6 = _isArray6 ? _iterator6 : _getIterator(_iterator6);;) {
            var _ref7;

            if (_isArray6) {
              if (_i7 >= _iterator6.length) break;
              _ref7 = _iterator6[_i7++];
            } else {
              _i7 = _iterator6.next();
              if (_i7.done) break;
              _ref7 = _i7.value;
            }

            var _group = _ref7;

            if (gid === _group.glyphID) {
              _res4.push.apply(_res4, range(_group.startCharCode, _group.endCharCode + 1));
            }
          }

          return _res4;
        }

      default:
        throw new Error('Unknown cmap format ' + cmap.version);
    }
  };

  return CmapProcessor;
}(), (_applyDecoratedDescriptor$1(_class$1.prototype, 'getCharacterSet', [cache], _Object$getOwnPropertyDescriptor(_class$1.prototype, 'getCharacterSet'), _class$1.prototype), _applyDecoratedDescriptor$1(_class$1.prototype, 'codePointsForGlyph', [cache], _Object$getOwnPropertyDescriptor(_class$1.prototype, 'codePointsForGlyph'), _class$1.prototype)), _class$1);

var KernProcessor = function () {
  function KernProcessor(font) {
    _classCallCheck(this, KernProcessor);

    this.kern = font.kern;
  }

  KernProcessor.prototype.process = function process(glyphs, positions) {
    for (var glyphIndex = 0; glyphIndex < glyphs.length - 1; glyphIndex++) {
      var left = glyphs[glyphIndex].id;
      var right = glyphs[glyphIndex + 1].id;
      positions[glyphIndex].xAdvance += this.getKerning(left, right);
    }
  };

  KernProcessor.prototype.getKerning = function getKerning(left, right) {
    var res = 0;

    for (var _iterator = this.kern.tables, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var table = _ref;

      if (table.coverage.crossStream) {
        continue;
      }

      switch (table.version) {
        case 0:
          if (!table.coverage.horizontal) {
            continue;
          }

          break;
        case 1:
          if (table.coverage.vertical || table.coverage.variation) {
            continue;
          }

          break;
        default:
          throw new Error('Unsupported kerning table version ' + table.version);
      }

      var val = 0;
      var s = table.subtable;
      switch (table.format) {
        case 0:
          var pairIdx = binarySearch(s.pairs, function (pair) {
            return left - pair.left || right - pair.right;
          });

          if (pairIdx >= 0) {
            val = s.pairs[pairIdx].value;
          }

          break;

        case 2:
          var leftOffset = 0,
              rightOffset = 0;
          if (left >= s.leftTable.firstGlyph && left < s.leftTable.firstGlyph + s.leftTable.nGlyphs) {
            leftOffset = s.leftTable.offsets[left - s.leftTable.firstGlyph];
          } else {
            leftOffset = s.array.off;
          }

          if (right >= s.rightTable.firstGlyph && right < s.rightTable.firstGlyph + s.rightTable.nGlyphs) {
            rightOffset = s.rightTable.offsets[right - s.rightTable.firstGlyph];
          }

          var index = (leftOffset + rightOffset - s.array.off) / 2;
          val = s.array.values.get(index);
          break;

        case 3:
          if (left >= s.glyphCount || right >= s.glyphCount) {
            return 0;
          }

          val = s.kernValue[s.kernIndex[s.leftClass[left] * s.rightClassCount + s.rightClass[right]]];
          break;

        default:
          throw new Error('Unsupported kerning sub-table format ' + table.format);
      }

      // Microsoft supports the override flag, which resets the result
      // Otherwise, the sum of the results from all subtables is returned
      if (table.coverage.override) {
        res = val;
      } else {
        res += val;
      }
    }

    return res;
  };

  return KernProcessor;
}();

/**
 * This class is used when GPOS does not define 'mark' or 'mkmk' features
 * for positioning marks relative to base glyphs. It uses the unicode
 * combining class property to position marks.
 *
 * Based on code from Harfbuzz, thanks!
 * https://github.com/behdad/harfbuzz/blob/master/src/hb-ot-shape-fallback.cc
 */

var UnicodeLayoutEngine = function () {
  function UnicodeLayoutEngine(font) {
    _classCallCheck(this, UnicodeLayoutEngine);

    this.font = font;
  }

  UnicodeLayoutEngine.prototype.positionGlyphs = function positionGlyphs(glyphs, positions) {
    // find each base + mark cluster, and position the marks relative to the base
    var clusterStart = 0;
    var clusterEnd = 0;
    for (var index = 0; index < glyphs.length; index++) {
      var glyph = glyphs[index];
      if (glyph.isMark) {
        // TODO: handle ligatures
        clusterEnd = index;
      } else {
        if (clusterStart !== clusterEnd) {
          this.positionCluster(glyphs, positions, clusterStart, clusterEnd);
        }

        clusterStart = clusterEnd = index;
      }
    }

    if (clusterStart !== clusterEnd) {
      this.positionCluster(glyphs, positions, clusterStart, clusterEnd);
    }

    return positions;
  };

  UnicodeLayoutEngine.prototype.positionCluster = function positionCluster(glyphs, positions, clusterStart, clusterEnd) {
    var base = glyphs[clusterStart];
    var baseBox = base.cbox.copy();

    // adjust bounding box for ligature glyphs
    if (base.codePoints.length > 1) {
      // LTR. TODO: RTL support.
      baseBox.minX += (base.codePoints.length - 1) * baseBox.width / base.codePoints.length;
    }

    var xOffset = -positions[clusterStart].xAdvance;
    var yOffset = 0;
    var yGap = this.font.unitsPerEm / 16;

    // position each of the mark glyphs relative to the base glyph
    for (var index = clusterStart + 1; index <= clusterEnd; index++) {
      var mark = glyphs[index];
      var markBox = mark.cbox;
      var position = positions[index];

      var combiningClass = this.getCombiningClass(mark.codePoints[0]);

      if (combiningClass !== 'Not_Reordered') {
        position.xOffset = position.yOffset = 0;

        // x positioning
        switch (combiningClass) {
          case 'Double_Above':
          case 'Double_Below':
            // LTR. TODO: RTL support.
            position.xOffset += baseBox.minX - markBox.width / 2 - markBox.minX;
            break;

          case 'Attached_Below_Left':
          case 'Below_Left':
          case 'Above_Left':
            // left align
            position.xOffset += baseBox.minX - markBox.minX;
            break;

          case 'Attached_Above_Right':
          case 'Below_Right':
          case 'Above_Right':
            // right align
            position.xOffset += baseBox.maxX - markBox.width - markBox.minX;
            break;

          default:
            // Attached_Below, Attached_Above, Below, Above, other
            // center align
            position.xOffset += baseBox.minX + (baseBox.width - markBox.width) / 2 - markBox.minX;
        }

        // y positioning
        switch (combiningClass) {
          case 'Double_Below':
          case 'Below_Left':
          case 'Below':
          case 'Below_Right':
          case 'Attached_Below_Left':
          case 'Attached_Below':
            // add a small gap between the glyphs if they are not attached
            if (combiningClass === 'Attached_Below_Left' || combiningClass === 'Attached_Below') {
              baseBox.minY += yGap;
            }

            position.yOffset = -baseBox.minY - markBox.maxY;
            baseBox.minY += markBox.height;
            break;

          case 'Double_Above':
          case 'Above_Left':
          case 'Above':
          case 'Above_Right':
          case 'Attached_Above':
          case 'Attached_Above_Right':
            // add a small gap between the glyphs if they are not attached
            if (combiningClass === 'Attached_Above' || combiningClass === 'Attached_Above_Right') {
              baseBox.maxY += yGap;
            }

            position.yOffset = baseBox.maxY - markBox.minY;
            baseBox.maxY += markBox.height;
            break;
        }

        position.xAdvance = position.yAdvance = 0;
        position.xOffset += xOffset;
        position.yOffset += yOffset;
      } else {
        xOffset -= position.xAdvance;
        yOffset -= position.yAdvance;
      }
    }

    return;
  };

  UnicodeLayoutEngine.prototype.getCombiningClass = function getCombiningClass(codePoint) {
    var combiningClass = unicode.getCombiningClass(codePoint);

    // Thai / Lao need some per-character work
    if ((codePoint & ~0xff) === 0x0e00) {
      if (combiningClass === 'Not_Reordered') {
        switch (codePoint) {
          case 0x0e31:
          case 0x0e34:
          case 0x0e35:
          case 0x0e36:
          case 0x0e37:
          case 0x0e47:
          case 0x0e4c:
          case 0x0e3d:
          case 0x0e4e:
            return 'Above_Right';

          case 0x0eb1:
          case 0x0eb4:
          case 0x0eb5:
          case 0x0eb6:
          case 0x0eb7:
          case 0x0ebb:
          case 0x0ecc:
          case 0x0ecd:
            return 'Above';

          case 0x0ebc:
            return 'Below';
        }
      } else if (codePoint === 0x0e3a) {
        // virama
        return 'Below_Right';
      }
    }

    switch (combiningClass) {
      // Hebrew

      case 'CCC10': // sheva
      case 'CCC11': // hataf segol
      case 'CCC12': // hataf patah
      case 'CCC13': // hataf qamats
      case 'CCC14': // hiriq
      case 'CCC15': // tsere
      case 'CCC16': // segol
      case 'CCC17': // patah
      case 'CCC18': // qamats
      case 'CCC20': // qubuts
      case 'CCC22':
        // meteg
        return 'Below';

      case 'CCC23':
        // rafe
        return 'Attached_Above';

      case 'CCC24':
        // shin dot
        return 'Above_Right';

      case 'CCC25': // sin dot
      case 'CCC19':
        // holam
        return 'Above_Left';

      case 'CCC26':
        // point varika
        return 'Above';

      case 'CCC21':
        // dagesh
        break;

      // Arabic and Syriac

      case 'CCC27': // fathatan
      case 'CCC28': // dammatan
      case 'CCC30': // fatha
      case 'CCC31': // damma
      case 'CCC33': // shadda
      case 'CCC34': // sukun
      case 'CCC35': // superscript alef
      case 'CCC36':
        // superscript alaph
        return 'Above';

      case 'CCC29': // kasratan
      case 'CCC32':
        // kasra
        return 'Below';

      // Thai

      case 'CCC103':
        // sara u / sara uu
        return 'Below_Right';

      case 'CCC107':
        // mai
        return 'Above_Right';

      // Lao

      case 'CCC118':
        // sign u / sign uu
        return 'Below';

      case 'CCC122':
        // mai
        return 'Above';

      // Tibetan

      case 'CCC129': // sign aa
      case 'CCC132':
        // sign u
        return 'Below';

      case 'CCC130':
        // sign i
        return 'Above';
    }

    return combiningClass;
  };

  return UnicodeLayoutEngine;
}();

/**
 * Represents a glyph bounding box
 */
var BBox = function () {
  function BBox() {
    var minX = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Infinity;
    var minY = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Infinity;
    var maxX = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : -Infinity;
    var maxY = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : -Infinity;

    _classCallCheck(this, BBox);

    /**
     * The minimum X position in the bounding box
     * @type {number}
     */
    this.minX = minX;

    /**
     * The minimum Y position in the bounding box
     * @type {number}
     */
    this.minY = minY;

    /**
     * The maxmimum X position in the bounding box
     * @type {number}
     */
    this.maxX = maxX;

    /**
     * The maxmimum Y position in the bounding box
     * @type {number}
     */
    this.maxY = maxY;
  }

  /**
   * The width of the bounding box
   * @type {number}
   */


  BBox.prototype.addPoint = function addPoint(x, y) {
    if (Math.abs(x) !== Infinity) {
      if (x < this.minX) {
        this.minX = x;
      }

      if (x > this.maxX) {
        this.maxX = x;
      }
    }

    if (Math.abs(y) !== Infinity) {
      if (y < this.minY) {
        this.minY = y;
      }

      if (y > this.maxY) {
        this.maxY = y;
      }
    }
  };

  BBox.prototype.copy = function copy() {
    return new BBox(this.minX, this.minY, this.maxX, this.maxY);
  };

  _createClass(BBox, [{
    key: "width",
    get: function get() {
      return this.maxX - this.minX;
    }

    /**
     * The height of the bounding box
     * @type {number}
     */

  }, {
    key: "height",
    get: function get() {
      return this.maxY - this.minY;
    }
  }]);

  return BBox;
}();

// This maps the Unicode Script property to an OpenType script tag
// Data from http://www.microsoft.com/typography/otspec/scripttags.htm
// and http://www.unicode.org/Public/UNIDATA/PropertyValueAliases.txt.
var UNICODE_SCRIPTS = {
  Caucasian_Albanian: 'aghb',
  Arabic: 'arab',
  Imperial_Aramaic: 'armi',
  Armenian: 'armn',
  Avestan: 'avst',
  Balinese: 'bali',
  Bamum: 'bamu',
  Bassa_Vah: 'bass',
  Batak: 'batk',
  Bengali: ['bng2', 'beng'],
  Bopomofo: 'bopo',
  Brahmi: 'brah',
  Braille: 'brai',
  Buginese: 'bugi',
  Buhid: 'buhd',
  Chakma: 'cakm',
  Canadian_Aboriginal: 'cans',
  Carian: 'cari',
  Cham: 'cham',
  Cherokee: 'cher',
  Coptic: 'copt',
  Cypriot: 'cprt',
  Cyrillic: 'cyrl',
  Devanagari: ['dev2', 'deva'],
  Deseret: 'dsrt',
  Duployan: 'dupl',
  Egyptian_Hieroglyphs: 'egyp',
  Elbasan: 'elba',
  Ethiopic: 'ethi',
  Georgian: 'geor',
  Glagolitic: 'glag',
  Gothic: 'goth',
  Grantha: 'gran',
  Greek: 'grek',
  Gujarati: ['gjr2', 'gujr'],
  Gurmukhi: ['gur2', 'guru'],
  Hangul: 'hang',
  Han: 'hani',
  Hanunoo: 'hano',
  Hebrew: 'hebr',
  Hiragana: 'hira',
  Pahawh_Hmong: 'hmng',
  Katakana_Or_Hiragana: 'hrkt',
  Old_Italic: 'ital',
  Javanese: 'java',
  Kayah_Li: 'kali',
  Katakana: 'kana',
  Kharoshthi: 'khar',
  Khmer: 'khmr',
  Khojki: 'khoj',
  Kannada: ['knd2', 'knda'],
  Kaithi: 'kthi',
  Tai_Tham: 'lana',
  Lao: 'lao ',
  Latin: 'latn',
  Lepcha: 'lepc',
  Limbu: 'limb',
  Linear_A: 'lina',
  Linear_B: 'linb',
  Lisu: 'lisu',
  Lycian: 'lyci',
  Lydian: 'lydi',
  Mahajani: 'mahj',
  Mandaic: 'mand',
  Manichaean: 'mani',
  Mende_Kikakui: 'mend',
  Meroitic_Cursive: 'merc',
  Meroitic_Hieroglyphs: 'mero',
  Malayalam: ['mlm2', 'mlym'],
  Modi: 'modi',
  Mongolian: 'mong',
  Mro: 'mroo',
  Meetei_Mayek: 'mtei',
  Myanmar: ['mym2', 'mymr'],
  Old_North_Arabian: 'narb',
  Nabataean: 'nbat',
  Nko: 'nko ',
  Ogham: 'ogam',
  Ol_Chiki: 'olck',
  Old_Turkic: 'orkh',
  Oriya: ['ory2', 'orya'],
  Osmanya: 'osma',
  Palmyrene: 'palm',
  Pau_Cin_Hau: 'pauc',
  Old_Permic: 'perm',
  Phags_Pa: 'phag',
  Inscriptional_Pahlavi: 'phli',
  Psalter_Pahlavi: 'phlp',
  Phoenician: 'phnx',
  Miao: 'plrd',
  Inscriptional_Parthian: 'prti',
  Rejang: 'rjng',
  Runic: 'runr',
  Samaritan: 'samr',
  Old_South_Arabian: 'sarb',
  Saurashtra: 'saur',
  Shavian: 'shaw',
  Sharada: 'shrd',
  Siddham: 'sidd',
  Khudawadi: 'sind',
  Sinhala: 'sinh',
  Sora_Sompeng: 'sora',
  Sundanese: 'sund',
  Syloti_Nagri: 'sylo',
  Syriac: 'syrc',
  Tagbanwa: 'tagb',
  Takri: 'takr',
  Tai_Le: 'tale',
  New_Tai_Lue: 'talu',
  Tamil: ['tml2', 'taml'],
  Tai_Viet: 'tavt',
  Telugu: ['tel2', 'telu'],
  Tifinagh: 'tfng',
  Tagalog: 'tglg',
  Thaana: 'thaa',
  Thai: 'thai',
  Tibetan: 'tibt',
  Tirhuta: 'tirh',
  Ugaritic: 'ugar',
  Vai: 'vai ',
  Warang_Citi: 'wara',
  Old_Persian: 'xpeo',
  Cuneiform: 'xsux',
  Yi: 'yi  ',
  Inherited: 'zinh',
  Common: 'zyyy',
  Unknown: 'zzzz'
};

var OPENTYPE_SCRIPTS = {};
for (var script in UNICODE_SCRIPTS) {
  var tag = UNICODE_SCRIPTS[script];
  if (Array.isArray(tag)) {
    for (var _iterator = tag, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var t = _ref;

      OPENTYPE_SCRIPTS[t] = script;
    }
  } else {
    OPENTYPE_SCRIPTS[tag] = script;
  }
}

function fromOpenType(tag) {
  return OPENTYPE_SCRIPTS[tag];
}

function forString(string) {
  var len = string.length;
  var idx = 0;
  while (idx < len) {
    var code = string.charCodeAt(idx++);

    // Check if this is a high surrogate
    if (0xd800 <= code && code <= 0xdbff && idx < len) {
      var next = string.charCodeAt(idx);

      // Check if this is a low surrogate
      if (0xdc00 <= next && next <= 0xdfff) {
        idx++;
        code = ((code & 0x3FF) << 10) + (next & 0x3FF) + 0x10000;
      }
    }

    var _script = unicode.getScript(code);
    if (_script !== 'Common' && _script !== 'Inherited' && _script !== 'Unknown') {
      return UNICODE_SCRIPTS[_script];
    }
  }

  return UNICODE_SCRIPTS.Unknown;
}

function forCodePoints(codePoints) {
  for (var i = 0; i < codePoints.length; i++) {
    var codePoint = codePoints[i];
    var _script2 = unicode.getScript(codePoint);
    if (_script2 !== 'Common' && _script2 !== 'Inherited' && _script2 !== 'Unknown') {
      return UNICODE_SCRIPTS[_script2];
    }
  }

  return UNICODE_SCRIPTS.Unknown;
}

// The scripts in this map are written from right to left
var RTL = {
  arab: true, // Arabic
  hebr: true, // Hebrew
  syrc: true, // Syriac
  thaa: true, // Thaana
  cprt: true, // Cypriot Syllabary
  khar: true, // Kharosthi
  phnx: true, // Phoenician
  'nko ': true, // N'Ko
  lydi: true, // Lydian
  avst: true, // Avestan
  armi: true, // Imperial Aramaic
  phli: true, // Inscriptional Pahlavi
  prti: true, // Inscriptional Parthian
  sarb: true, // Old South Arabian
  orkh: true, // Old Turkic, Orkhon Runic
  samr: true, // Samaritan
  mand: true, // Mandaic, Mandaean
  merc: true, // Meroitic Cursive
  mero: true, // Meroitic Hieroglyphs

  // Unicode 7.0 (not listed on http://www.microsoft.com/typography/otspec/scripttags.htm)
  mani: true, // Manichaean
  mend: true, // Mende Kikakui
  nbat: true, // Nabataean
  narb: true, // Old North Arabian
  palm: true, // Palmyrene
  phlp: true // Psalter Pahlavi
};

function direction(script) {
  if (RTL[script]) {
    return 'rtl';
  }

  return 'ltr';
}

/**
 * Represents a run of Glyph and GlyphPosition objects.
 * Returned by the font layout method.
 */

var GlyphRun = function () {
  function GlyphRun(glyphs, features, script, language, direction$$) {
    _classCallCheck(this, GlyphRun);

    /**
     * An array of Glyph objects in the run
     * @type {Glyph[]}
     */
    this.glyphs = glyphs;

    /**
     * An array of GlyphPosition objects for each glyph in the run
     * @type {GlyphPosition[]}
     */
    this.positions = null;

    /**
     * The script that was requested for shaping. This was either passed in or detected automatically.
     * @type {string}
     */
    this.script = script;

    /**
     * The language requested for shaping, as passed in. If `null`, the default language for the
     * script was used.
     * @type {string}
     */
    this.language = language || null;

    /**
     * The direction requested for shaping, as passed in (either ltr or rtl).
     * If `null`, the default direction of the script is used.
     * @type {string}
     */
    this.direction = direction$$ || direction(script);

    /**
     * The features requested during shaping. This is a combination of user
     * specified features and features chosen by the shaper.
     * @type {object}
     */
    this.features = {};

    // Convert features to an object
    if (Array.isArray(features)) {
      for (var _iterator = features, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
        var _ref;

        if (_isArray) {
          if (_i >= _iterator.length) break;
          _ref = _iterator[_i++];
        } else {
          _i = _iterator.next();
          if (_i.done) break;
          _ref = _i.value;
        }

        var tag = _ref;

        this.features[tag] = true;
      }
    } else if ((typeof features === 'undefined' ? 'undefined' : _typeof(features)) === 'object') {
      this.features = features;
    }
  }

  /**
   * The total advance width of the run.
   * @type {number}
   */


  _createClass(GlyphRun, [{
    key: 'advanceWidth',
    get: function get() {
      var width = 0;
      for (var _iterator2 = this.positions, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
        var _ref2;

        if (_isArray2) {
          if (_i2 >= _iterator2.length) break;
          _ref2 = _iterator2[_i2++];
        } else {
          _i2 = _iterator2.next();
          if (_i2.done) break;
          _ref2 = _i2.value;
        }

        var position = _ref2;

        width += position.xAdvance;
      }

      return width;
    }

    /**
     * The total advance height of the run.
     * @type {number}
     */

  }, {
    key: 'advanceHeight',
    get: function get() {
      var height = 0;
      for (var _iterator3 = this.positions, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
        var _ref3;

        if (_isArray3) {
          if (_i3 >= _iterator3.length) break;
          _ref3 = _iterator3[_i3++];
        } else {
          _i3 = _iterator3.next();
          if (_i3.done) break;
          _ref3 = _i3.value;
        }

        var position = _ref3;

        height += position.yAdvance;
      }

      return height;
    }

    /**
     * The bounding box containing all glyphs in the run.
     * @type {BBox}
     */

  }, {
    key: 'bbox',
    get: function get() {
      var bbox = new BBox();

      var x = 0;
      var y = 0;
      for (var index = 0; index < this.glyphs.length; index++) {
        var glyph = this.glyphs[index];
        var p = this.positions[index];
        var b = glyph.bbox;

        bbox.addPoint(b.minX + x + p.xOffset, b.minY + y + p.yOffset);
        bbox.addPoint(b.maxX + x + p.xOffset, b.maxY + y + p.yOffset);

        x += p.xAdvance;
        y += p.yAdvance;
      }

      return bbox;
    }
  }]);

  return GlyphRun;
}();

/**
 * Represents positioning information for a glyph in a GlyphRun.
 */
var GlyphPosition = function GlyphPosition() {
  var xAdvance = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var yAdvance = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var xOffset = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var yOffset = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;

  _classCallCheck(this, GlyphPosition);

  /**
   * The amount to move the virtual pen in the X direction after rendering this glyph.
   * @type {number}
   */
  this.xAdvance = xAdvance;

  /**
   * The amount to move the virtual pen in the Y direction after rendering this glyph.
   * @type {number}
   */
  this.yAdvance = yAdvance;

  /**
   * The offset from the pen position in the X direction at which to render this glyph.
   * @type {number}
   */
  this.xOffset = xOffset;

  /**
   * The offset from the pen position in the Y direction at which to render this glyph.
   * @type {number}
   */
  this.yOffset = yOffset;
};

// see https://developer.apple.com/fonts/TrueType-Reference-Manual/RM09/AppendixF.html
// and /System/Library/Frameworks/CoreText.framework/Versions/A/Headers/SFNTLayoutTypes.h on a Mac
var features = {
  allTypographicFeatures: {
    code: 0,
    exclusive: false,
    allTypeFeatures: 0
  },
  ligatures: {
    code: 1,
    exclusive: false,
    requiredLigatures: 0,
    commonLigatures: 2,
    rareLigatures: 4,
    // logos: 6
    rebusPictures: 8,
    diphthongLigatures: 10,
    squaredLigatures: 12,
    abbrevSquaredLigatures: 14,
    symbolLigatures: 16,
    contextualLigatures: 18,
    historicalLigatures: 20
  },
  cursiveConnection: {
    code: 2,
    exclusive: true,
    unconnected: 0,
    partiallyConnected: 1,
    cursive: 2
  },
  letterCase: {
    code: 3,
    exclusive: true
  },
  // upperAndLowerCase: 0          # deprecated
  // allCaps: 1                    # deprecated
  // allLowerCase: 2               # deprecated
  // smallCaps: 3                  # deprecated
  // initialCaps: 4                # deprecated
  // initialCapsAndSmallCaps: 5    # deprecated
  verticalSubstitution: {
    code: 4,
    exclusive: false,
    substituteVerticalForms: 0
  },
  linguisticRearrangement: {
    code: 5,
    exclusive: false,
    linguisticRearrangement: 0
  },
  numberSpacing: {
    code: 6,
    exclusive: true,
    monospacedNumbers: 0,
    proportionalNumbers: 1,
    thirdWidthNumbers: 2,
    quarterWidthNumbers: 3
  },
  smartSwash: {
    code: 8,
    exclusive: false,
    wordInitialSwashes: 0,
    wordFinalSwashes: 2,
    // lineInitialSwashes: 4
    // lineFinalSwashes: 6
    nonFinalSwashes: 8
  },
  diacritics: {
    code: 9,
    exclusive: true,
    showDiacritics: 0,
    hideDiacritics: 1,
    decomposeDiacritics: 2
  },
  verticalPosition: {
    code: 10,
    exclusive: true,
    normalPosition: 0,
    superiors: 1,
    inferiors: 2,
    ordinals: 3,
    scientificInferiors: 4
  },
  fractions: {
    code: 11,
    exclusive: true,
    noFractions: 0,
    verticalFractions: 1,
    diagonalFractions: 2
  },
  overlappingCharacters: {
    code: 13,
    exclusive: false,
    preventOverlap: 0
  },
  typographicExtras: {
    code: 14,
    exclusive: false,
    // hyphensToEmDash: 0
    // hyphenToEnDash: 2
    slashedZero: 4
  },
  // formInterrobang: 6
  // smartQuotes: 8
  // periodsToEllipsis: 10
  mathematicalExtras: {
    code: 15,
    exclusive: false,
    // hyphenToMinus: 0
    // asteristoMultiply: 2
    // slashToDivide: 4
    // inequalityLigatures: 6
    // exponents: 8
    mathematicalGreek: 10
  },
  ornamentSets: {
    code: 16,
    exclusive: true,
    noOrnaments: 0,
    dingbats: 1,
    piCharacters: 2,
    fleurons: 3,
    decorativeBorders: 4,
    internationalSymbols: 5,
    mathSymbols: 6
  },
  characterAlternatives: {
    code: 17,
    exclusive: true,
    noAlternates: 0
  },
  // user defined options
  designComplexity: {
    code: 18,
    exclusive: true,
    designLevel1: 0,
    designLevel2: 1,
    designLevel3: 2,
    designLevel4: 3,
    designLevel5: 4
  },
  styleOptions: {
    code: 19,
    exclusive: true,
    noStyleOptions: 0,
    displayText: 1,
    engravedText: 2,
    illuminatedCaps: 3,
    titlingCaps: 4,
    tallCaps: 5
  },
  characterShape: {
    code: 20,
    exclusive: true,
    traditionalCharacters: 0,
    simplifiedCharacters: 1,
    JIS1978Characters: 2,
    JIS1983Characters: 3,
    JIS1990Characters: 4,
    traditionalAltOne: 5,
    traditionalAltTwo: 6,
    traditionalAltThree: 7,
    traditionalAltFour: 8,
    traditionalAltFive: 9,
    expertCharacters: 10,
    JIS2004Characters: 11,
    hojoCharacters: 12,
    NLCCharacters: 13,
    traditionalNamesCharacters: 14
  },
  numberCase: {
    code: 21,
    exclusive: true,
    lowerCaseNumbers: 0,
    upperCaseNumbers: 1
  },
  textSpacing: {
    code: 22,
    exclusive: true,
    proportionalText: 0,
    monospacedText: 1,
    halfWidthText: 2,
    thirdWidthText: 3,
    quarterWidthText: 4,
    altProportionalText: 5,
    altHalfWidthText: 6
  },
  transliteration: {
    code: 23,
    exclusive: true,
    noTransliteration: 0
  },
  // hanjaToHangul: 1
  // hiraganaToKatakana: 2
  // katakanaToHiragana: 3
  // kanaToRomanization: 4
  // romanizationToHiragana: 5
  // romanizationToKatakana: 6
  // hanjaToHangulAltOne: 7
  // hanjaToHangulAltTwo: 8
  // hanjaToHangulAltThree: 9
  annotation: {
    code: 24,
    exclusive: true,
    noAnnotation: 0,
    boxAnnotation: 1,
    roundedBoxAnnotation: 2,
    circleAnnotation: 3,
    invertedCircleAnnotation: 4,
    parenthesisAnnotation: 5,
    periodAnnotation: 6,
    romanNumeralAnnotation: 7,
    diamondAnnotation: 8,
    invertedBoxAnnotation: 9,
    invertedRoundedBoxAnnotation: 10
  },
  kanaSpacing: {
    code: 25,
    exclusive: true,
    fullWidthKana: 0,
    proportionalKana: 1
  },
  ideographicSpacing: {
    code: 26,
    exclusive: true,
    fullWidthIdeographs: 0,
    proportionalIdeographs: 1,
    halfWidthIdeographs: 2
  },
  unicodeDecomposition: {
    code: 27,
    exclusive: false,
    canonicalComposition: 0,
    compatibilityComposition: 2,
    transcodingComposition: 4
  },
  rubyKana: {
    code: 28,
    exclusive: false,
    // noRubyKana: 0     # deprecated - use rubyKanaOff instead
    // rubyKana: 1     # deprecated - use rubyKanaOn instead
    rubyKana: 2
  },
  CJKSymbolAlternatives: {
    code: 29,
    exclusive: true,
    noCJKSymbolAlternatives: 0,
    CJKSymbolAltOne: 1,
    CJKSymbolAltTwo: 2,
    CJKSymbolAltThree: 3,
    CJKSymbolAltFour: 4,
    CJKSymbolAltFive: 5
  },
  ideographicAlternatives: {
    code: 30,
    exclusive: true,
    noIdeographicAlternatives: 0,
    ideographicAltOne: 1,
    ideographicAltTwo: 2,
    ideographicAltThree: 3,
    ideographicAltFour: 4,
    ideographicAltFive: 5
  },
  CJKVerticalRomanPlacement: {
    code: 31,
    exclusive: true,
    CJKVerticalRomanCentered: 0,
    CJKVerticalRomanHBaseline: 1
  },
  italicCJKRoman: {
    code: 32,
    exclusive: false,
    // noCJKItalicRoman: 0     # deprecated - use CJKItalicRomanOff instead
    // CJKItalicRoman: 1     # deprecated - use CJKItalicRomanOn instead
    CJKItalicRoman: 2
  },
  caseSensitiveLayout: {
    code: 33,
    exclusive: false,
    caseSensitiveLayout: 0,
    caseSensitiveSpacing: 2
  },
  alternateKana: {
    code: 34,
    exclusive: false,
    alternateHorizKana: 0,
    alternateVertKana: 2
  },
  stylisticAlternatives: {
    code: 35,
    exclusive: false,
    noStylisticAlternates: 0,
    stylisticAltOne: 2,
    stylisticAltTwo: 4,
    stylisticAltThree: 6,
    stylisticAltFour: 8,
    stylisticAltFive: 10,
    stylisticAltSix: 12,
    stylisticAltSeven: 14,
    stylisticAltEight: 16,
    stylisticAltNine: 18,
    stylisticAltTen: 20,
    stylisticAltEleven: 22,
    stylisticAltTwelve: 24,
    stylisticAltThirteen: 26,
    stylisticAltFourteen: 28,
    stylisticAltFifteen: 30,
    stylisticAltSixteen: 32,
    stylisticAltSeventeen: 34,
    stylisticAltEighteen: 36,
    stylisticAltNineteen: 38,
    stylisticAltTwenty: 40
  },
  contextualAlternates: {
    code: 36,
    exclusive: false,
    contextualAlternates: 0,
    swashAlternates: 2,
    contextualSwashAlternates: 4
  },
  lowerCase: {
    code: 37,
    exclusive: true,
    defaultLowerCase: 0,
    lowerCaseSmallCaps: 1,
    lowerCasePetiteCaps: 2
  },
  upperCase: {
    code: 38,
    exclusive: true,
    defaultUpperCase: 0,
    upperCaseSmallCaps: 1,
    upperCasePetiteCaps: 2
  },
  languageTag: { // indices into ltag table
    code: 39,
    exclusive: true
  },
  CJKRomanSpacing: {
    code: 103,
    exclusive: true,
    halfWidthCJKRoman: 0,
    proportionalCJKRoman: 1,
    defaultCJKRoman: 2,
    fullWidthCJKRoman: 3
  }
};

var feature = function feature(name, selector) {
  return [features[name].code, features[name][selector]];
};

var OTMapping = {
  rlig: feature('ligatures', 'requiredLigatures'),
  clig: feature('ligatures', 'contextualLigatures'),
  dlig: feature('ligatures', 'rareLigatures'),
  hlig: feature('ligatures', 'historicalLigatures'),
  liga: feature('ligatures', 'commonLigatures'),
  hist: feature('ligatures', 'historicalLigatures'), // ??

  smcp: feature('lowerCase', 'lowerCaseSmallCaps'),
  pcap: feature('lowerCase', 'lowerCasePetiteCaps'),

  frac: feature('fractions', 'diagonalFractions'),
  dnom: feature('fractions', 'diagonalFractions'), // ??
  numr: feature('fractions', 'diagonalFractions'), // ??
  afrc: feature('fractions', 'verticalFractions'),
  // aalt
  // abvf, abvm, abvs, akhn, blwf, blwm, blws, cfar, cjct, cpsp, falt, isol, jalt, ljmo, mset?
  // ltra, ltrm, nukt, pref, pres, pstf, psts, rand, rkrf, rphf, rtla, rtlm, size, tjmo, tnum?
  // unic, vatu, vhal, vjmo, vpal, vrt2
  // dist -> trak table?
  // kern, vkrn -> kern table
  // lfbd + opbd + rtbd -> opbd table?
  // mark, mkmk -> acnt table?
  // locl -> languageTag + ltag table

  case: feature('caseSensitiveLayout', 'caseSensitiveLayout'), // also caseSensitiveSpacing
  ccmp: feature('unicodeDecomposition', 'canonicalComposition'), // compatibilityComposition?
  cpct: feature('CJKVerticalRomanPlacement', 'CJKVerticalRomanCentered'), // guess..., probably not given below
  valt: feature('CJKVerticalRomanPlacement', 'CJKVerticalRomanCentered'),
  swsh: feature('contextualAlternates', 'swashAlternates'),
  cswh: feature('contextualAlternates', 'contextualSwashAlternates'),
  curs: feature('cursiveConnection', 'cursive'), // ??
  c2pc: feature('upperCase', 'upperCasePetiteCaps'),
  c2sc: feature('upperCase', 'upperCaseSmallCaps'),

  init: feature('smartSwash', 'wordInitialSwashes'), // ??
  fin2: feature('smartSwash', 'wordFinalSwashes'), // ??
  medi: feature('smartSwash', 'nonFinalSwashes'), // ??
  med2: feature('smartSwash', 'nonFinalSwashes'), // ??
  fin3: feature('smartSwash', 'wordFinalSwashes'), // ??
  fina: feature('smartSwash', 'wordFinalSwashes'), // ??

  pkna: feature('kanaSpacing', 'proportionalKana'),
  half: feature('textSpacing', 'halfWidthText'), // also HalfWidthCJKRoman, HalfWidthIdeographs?
  halt: feature('textSpacing', 'altHalfWidthText'),

  hkna: feature('alternateKana', 'alternateHorizKana'),
  vkna: feature('alternateKana', 'alternateVertKana'),
  // hngl: feature 'transliteration', 'hanjaToHangulSelector' # deprecated

  ital: feature('italicCJKRoman', 'CJKItalicRoman'),
  lnum: feature('numberCase', 'upperCaseNumbers'),
  onum: feature('numberCase', 'lowerCaseNumbers'),
  mgrk: feature('mathematicalExtras', 'mathematicalGreek'),

  // nalt: not enough info. what type of annotation?
  // ornm: ditto, which ornament style?

  calt: feature('contextualAlternates', 'contextualAlternates'), // or more?
  vrt2: feature('verticalSubstitution', 'substituteVerticalForms'), // oh... below?
  vert: feature('verticalSubstitution', 'substituteVerticalForms'),
  tnum: feature('numberSpacing', 'monospacedNumbers'),
  pnum: feature('numberSpacing', 'proportionalNumbers'),
  sups: feature('verticalPosition', 'superiors'),
  subs: feature('verticalPosition', 'inferiors'),
  ordn: feature('verticalPosition', 'ordinals'),
  pwid: feature('textSpacing', 'proportionalText'),
  hwid: feature('textSpacing', 'halfWidthText'),
  qwid: feature('textSpacing', 'quarterWidthText'), // also QuarterWidthNumbers?
  twid: feature('textSpacing', 'thirdWidthText'), // also ThirdWidthNumbers?
  fwid: feature('textSpacing', 'proportionalText'), //??
  palt: feature('textSpacing', 'altProportionalText'),
  trad: feature('characterShape', 'traditionalCharacters'),
  smpl: feature('characterShape', 'simplifiedCharacters'),
  jp78: feature('characterShape', 'JIS1978Characters'),
  jp83: feature('characterShape', 'JIS1983Characters'),
  jp90: feature('characterShape', 'JIS1990Characters'),
  jp04: feature('characterShape', 'JIS2004Characters'),
  expt: feature('characterShape', 'expertCharacters'),
  hojo: feature('characterShape', 'hojoCharacters'),
  nlck: feature('characterShape', 'NLCCharacters'),
  tnam: feature('characterShape', 'traditionalNamesCharacters'),
  ruby: feature('rubyKana', 'rubyKana'),
  titl: feature('styleOptions', 'titlingCaps'),
  zero: feature('typographicExtras', 'slashedZero'),

  ss01: feature('stylisticAlternatives', 'stylisticAltOne'),
  ss02: feature('stylisticAlternatives', 'stylisticAltTwo'),
  ss03: feature('stylisticAlternatives', 'stylisticAltThree'),
  ss04: feature('stylisticAlternatives', 'stylisticAltFour'),
  ss05: feature('stylisticAlternatives', 'stylisticAltFive'),
  ss06: feature('stylisticAlternatives', 'stylisticAltSix'),
  ss07: feature('stylisticAlternatives', 'stylisticAltSeven'),
  ss08: feature('stylisticAlternatives', 'stylisticAltEight'),
  ss09: feature('stylisticAlternatives', 'stylisticAltNine'),
  ss10: feature('stylisticAlternatives', 'stylisticAltTen'),
  ss11: feature('stylisticAlternatives', 'stylisticAltEleven'),
  ss12: feature('stylisticAlternatives', 'stylisticAltTwelve'),
  ss13: feature('stylisticAlternatives', 'stylisticAltThirteen'),
  ss14: feature('stylisticAlternatives', 'stylisticAltFourteen'),
  ss15: feature('stylisticAlternatives', 'stylisticAltFifteen'),
  ss16: feature('stylisticAlternatives', 'stylisticAltSixteen'),
  ss17: feature('stylisticAlternatives', 'stylisticAltSeventeen'),
  ss18: feature('stylisticAlternatives', 'stylisticAltEighteen'),
  ss19: feature('stylisticAlternatives', 'stylisticAltNineteen'),
  ss20: feature('stylisticAlternatives', 'stylisticAltTwenty')
};

// salt: feature 'stylisticAlternatives', 'stylisticAltOne' # hmm, which one to choose

// Add cv01-cv99 features
for (var i = 1; i <= 99; i++) {
  OTMapping['cv' + ('00' + i).slice(-2)] = [features.characterAlternatives.code, i];
}

// create inverse mapping
var AATMapping = {};
for (var ot in OTMapping) {
  var aat = OTMapping[ot];
  if (AATMapping[aat[0]] == null) {
    AATMapping[aat[0]] = {};
  }

  AATMapping[aat[0]][aat[1]] = ot;
}

// Maps an array of OpenType features to AAT features
// in the form of {featureType:{featureSetting:true}}
function mapOTToAAT(features) {
  var res = {};
  for (var k in features) {
    var r = void 0;
    if (r = OTMapping[k]) {
      if (res[r[0]] == null) {
        res[r[0]] = {};
      }

      res[r[0]][r[1]] = features[k];
    }
  }

  return res;
}

// Maps strings in a [featureType, featureSetting]
// to their equivalent number codes
function mapFeatureStrings(f) {
  var type = f[0],
      setting = f[1];

  if (isNaN(type)) {
    var typeCode = features[type] && features[type].code;
  } else {
    var typeCode = type;
  }

  if (isNaN(setting)) {
    var settingCode = features[type] && features[type][setting];
  } else {
    var settingCode = setting;
  }

  return [typeCode, settingCode];
}

// Maps AAT features to an array of OpenType features
// Supports both arrays in the form of [[featureType, featureSetting]]
// and objects in the form of {featureType:{featureSetting:true}}
// featureTypes and featureSettings can be either strings or number codes
function mapAATToOT(features) {
  var res = {};
  if (Array.isArray(features)) {
    for (var k = 0; k < features.length; k++) {
      var r = void 0;
      var f = mapFeatureStrings(features[k]);
      if (r = AATMapping[f[0]] && AATMapping[f[0]][f[1]]) {
        res[r] = true;
      }
    }
  } else if ((typeof features === 'undefined' ? 'undefined' : _typeof(features)) === 'object') {
    for (var type in features) {
      var _feature = features[type];
      for (var setting in _feature) {
        var _r = void 0;
        var _f = mapFeatureStrings([type, setting]);
        if (_feature[setting] && (_r = AATMapping[_f[0]] && AATMapping[_f[0]][_f[1]])) {
          res[_r] = true;
        }
      }
    }
  }

  return _Object$keys(res);
}

var _class$3;
function _applyDecoratedDescriptor$3(target, property, decorators, descriptor, context) {
  var desc = {};
  Object['ke' + 'ys'](descriptor).forEach(function (key) {
    desc[key] = descriptor[key];
  });
  desc.enumerable = !!desc.enumerable;
  desc.configurable = !!desc.configurable;

  if ('value' in desc || desc.initializer) {
    desc.writable = true;
  }

  desc = decorators.slice().reverse().reduce(function (desc, decorator) {
    return decorator(target, property, desc) || desc;
  }, desc);

  if (context && desc.initializer !== void 0) {
    desc.value = desc.initializer ? desc.initializer.call(context) : void 0;
    desc.initializer = undefined;
  }

  if (desc.initializer === void 0) {
    Object['define' + 'Property'](target, property, desc);
    desc = null;
  }

  return desc;
}

var AATLookupTable = (_class$3 = function () {
  function AATLookupTable(table) {
    _classCallCheck(this, AATLookupTable);

    this.table = table;
  }

  AATLookupTable.prototype.lookup = function lookup(glyph) {
    switch (this.table.version) {
      case 0:
        // simple array format
        return this.table.values.getItem(glyph);

      case 2: // segment format
      case 4:
        {
          var min = 0;
          var max = this.table.binarySearchHeader.nUnits - 1;

          while (min <= max) {
            var mid = min + max >> 1;
            var seg = this.table.segments[mid];

            // special end of search value
            if (seg.firstGlyph === 0xffff) {
              return null;
            }

            if (glyph < seg.firstGlyph) {
              max = mid - 1;
            } else if (glyph > seg.lastGlyph) {
              min = mid + 1;
            } else {
              if (this.table.version === 2) {
                return seg.value;
              } else {
                return seg.values[glyph - seg.firstGlyph];
              }
            }
          }

          return null;
        }

      case 6:
        {
          // lookup single
          var _min = 0;
          var _max = this.table.binarySearchHeader.nUnits - 1;

          while (_min <= _max) {
            var mid = _min + _max >> 1;
            var seg = this.table.segments[mid];

            // special end of search value
            if (seg.glyph === 0xffff) {
              return null;
            }

            if (glyph < seg.glyph) {
              _max = mid - 1;
            } else if (glyph > seg.glyph) {
              _min = mid + 1;
            } else {
              return seg.value;
            }
          }

          return null;
        }

      case 8:
        // lookup trimmed
        return this.table.values[glyph - this.table.firstGlyph];

      default:
        throw new Error('Unknown lookup table format: ' + this.table.version);
    }
  };

  AATLookupTable.prototype.glyphsForValue = function glyphsForValue(classValue) {
    var res = [];

    switch (this.table.version) {
      case 2: // segment format
      case 4:
        {
          for (var _iterator = this.table.segments, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
            var _ref;

            if (_isArray) {
              if (_i >= _iterator.length) break;
              _ref = _iterator[_i++];
            } else {
              _i = _iterator.next();
              if (_i.done) break;
              _ref = _i.value;
            }

            var segment = _ref;

            if (this.table.version === 2 && segment.value === classValue) {
              res.push.apply(res, range(segment.firstGlyph, segment.lastGlyph + 1));
            } else {
              for (var index = 0; index < segment.values.length; index++) {
                if (segment.values[index] === classValue) {
                  res.push(segment.firstGlyph + index);
                }
              }
            }
          }

          break;
        }

      case 6:
        {
          // lookup single
          for (var _iterator2 = this.table.segments, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
            var _ref2;

            if (_isArray2) {
              if (_i2 >= _iterator2.length) break;
              _ref2 = _iterator2[_i2++];
            } else {
              _i2 = _iterator2.next();
              if (_i2.done) break;
              _ref2 = _i2.value;
            }

            var _segment = _ref2;

            if (_segment.value === classValue) {
              res.push(_segment.glyph);
            }
          }

          break;
        }

      case 8:
        {
          // lookup trimmed
          for (var i = 0; i < this.table.values.length; i++) {
            if (this.table.values[i] === classValue) {
              res.push(this.table.firstGlyph + i);
            }
          }

          break;
        }

      default:
        throw new Error('Unknown lookup table format: ' + this.table.version);
    }

    return res;
  };

  return AATLookupTable;
}(), (_applyDecoratedDescriptor$3(_class$3.prototype, 'glyphsForValue', [cache], _Object$getOwnPropertyDescriptor(_class$3.prototype, 'glyphsForValue'), _class$3.prototype)), _class$3);

var START_OF_TEXT_STATE = 0;
var END_OF_TEXT_CLASS = 0;
var OUT_OF_BOUNDS_CLASS = 1;
var DELETED_GLYPH_CLASS = 2;
var DONT_ADVANCE = 0x4000;

var AATStateMachine = function () {
  function AATStateMachine(stateTable) {
    _classCallCheck(this, AATStateMachine);

    this.stateTable = stateTable;
    this.lookupTable = new AATLookupTable(stateTable.classTable);
  }

  AATStateMachine.prototype.process = function process(glyphs, reverse, processEntry) {
    var currentState = START_OF_TEXT_STATE; // START_OF_LINE_STATE is used for kashida glyph insertions sometimes I think?
    var index = reverse ? glyphs.length - 1 : 0;
    var dir = reverse ? -1 : 1;

    while (dir === 1 && index <= glyphs.length || dir === -1 && index >= -1) {
      var glyph = null;
      var classCode = OUT_OF_BOUNDS_CLASS;
      var shouldAdvance = true;

      if (index === glyphs.length || index === -1) {
        classCode = END_OF_TEXT_CLASS;
      } else {
        glyph = glyphs[index];
        if (glyph.id === 0xffff) {
          // deleted glyph
          classCode = DELETED_GLYPH_CLASS;
        } else {
          classCode = this.lookupTable.lookup(glyph.id);
          if (classCode == null) {
            classCode = OUT_OF_BOUNDS_CLASS;
          }
        }
      }

      var row = this.stateTable.stateArray.getItem(currentState);
      var entryIndex = row[classCode];
      var entry = this.stateTable.entryTable.getItem(entryIndex);

      if (classCode !== END_OF_TEXT_CLASS && classCode !== DELETED_GLYPH_CLASS) {
        processEntry(glyph, entry, index);
        shouldAdvance = !(entry.flags & DONT_ADVANCE);
      }

      currentState = entry.newState;
      if (shouldAdvance) {
        index += dir;
      }
    }

    return glyphs;
  };

  /**
   * Performs a depth-first traversal of the glyph strings
   * represented by the state machine.
   */


  AATStateMachine.prototype.traverse = function traverse(opts) {
    var state = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var visited = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : new _Set();

    if (visited.has(state)) {
      return;
    }

    visited.add(state);

    var _stateTable = this.stateTable,
        nClasses = _stateTable.nClasses,
        stateArray = _stateTable.stateArray,
        entryTable = _stateTable.entryTable;

    var row = stateArray.getItem(state);

    // Skip predefined classes
    for (var classCode = 4; classCode < nClasses; classCode++) {
      var entryIndex = row[classCode];
      var entry = entryTable.getItem(entryIndex);

      // Try all glyphs in the class
      for (var _iterator = this.lookupTable.glyphsForValue(classCode), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
        var _ref;

        if (_isArray) {
          if (_i >= _iterator.length) break;
          _ref = _iterator[_i++];
        } else {
          _i = _iterator.next();
          if (_i.done) break;
          _ref = _i.value;
        }

        var glyph = _ref;

        if (opts.enter) {
          opts.enter(glyph, entry);
        }

        if (entry.newState !== 0) {
          this.traverse(opts, entry.newState, visited);
        }

        if (opts.exit) {
          opts.exit(glyph, entry);
        }
      }
    }
  };

  return AATStateMachine;
}();

var _class$2;
function _applyDecoratedDescriptor$2(target, property, decorators, descriptor, context) {
  var desc = {};
  Object['ke' + 'ys'](descriptor).forEach(function (key) {
    desc[key] = descriptor[key];
  });
  desc.enumerable = !!desc.enumerable;
  desc.configurable = !!desc.configurable;

  if ('value' in desc || desc.initializer) {
    desc.writable = true;
  }

  desc = decorators.slice().reverse().reduce(function (desc, decorator) {
    return decorator(target, property, desc) || desc;
  }, desc);

  if (context && desc.initializer !== void 0) {
    desc.value = desc.initializer ? desc.initializer.call(context) : void 0;
    desc.initializer = undefined;
  }

  if (desc.initializer === void 0) {
    Object['define' + 'Property'](target, property, desc);
    desc = null;
  }

  return desc;
}

// indic replacement flags
var MARK_FIRST = 0x8000;
var MARK_LAST = 0x2000;
var VERB = 0x000F;

// contextual substitution and glyph insertion flag
var SET_MARK = 0x8000;

// ligature entry flags
var SET_COMPONENT = 0x8000;
var PERFORM_ACTION = 0x2000;

// ligature action masks
var LAST_MASK = 0x80000000;
var STORE_MASK = 0x40000000;
var OFFSET_MASK = 0x3FFFFFFF;

var REVERSE_DIRECTION = 0x400000;
var CURRENT_INSERT_BEFORE = 0x0800;
var MARKED_INSERT_BEFORE = 0x0400;
var CURRENT_INSERT_COUNT = 0x03E0;
var MARKED_INSERT_COUNT = 0x001F;

var AATMorxProcessor = (_class$2 = function () {
  function AATMorxProcessor(font) {
    _classCallCheck(this, AATMorxProcessor);

    this.processIndicRearragement = this.processIndicRearragement.bind(this);
    this.processContextualSubstitution = this.processContextualSubstitution.bind(this);
    this.processLigature = this.processLigature.bind(this);
    this.processNoncontextualSubstitutions = this.processNoncontextualSubstitutions.bind(this);
    this.processGlyphInsertion = this.processGlyphInsertion.bind(this);
    this.font = font;
    this.morx = font.morx;
    this.inputCache = null;
  }

  // Processes an array of glyphs and applies the specified features
  // Features should be in the form of {featureType:{featureSetting:boolean}}


  AATMorxProcessor.prototype.process = function process(glyphs) {
    var features = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    for (var _iterator = this.morx.chains, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var chain = _ref;

      var flags = chain.defaultFlags;

      // enable/disable the requested features
      for (var _iterator2 = chain.features, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
        var _ref2;

        if (_isArray2) {
          if (_i2 >= _iterator2.length) break;
          _ref2 = _iterator2[_i2++];
        } else {
          _i2 = _iterator2.next();
          if (_i2.done) break;
          _ref2 = _i2.value;
        }

        var feature = _ref2;

        var f = void 0;
        if (f = features[feature.featureType]) {
          if (f[feature.featureSetting]) {
            flags &= feature.disableFlags;
            flags |= feature.enableFlags;
          } else if (f[feature.featureSetting] === false) {
            flags |= ~feature.disableFlags;
            flags &= ~feature.enableFlags;
          }
        }
      }

      for (var _iterator3 = chain.subtables, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
        var _ref3;

        if (_isArray3) {
          if (_i3 >= _iterator3.length) break;
          _ref3 = _iterator3[_i3++];
        } else {
          _i3 = _iterator3.next();
          if (_i3.done) break;
          _ref3 = _i3.value;
        }

        var subtable = _ref3;

        if (subtable.subFeatureFlags & flags) {
          this.processSubtable(subtable, glyphs);
        }
      }
    }

    // remove deleted glyphs
    var index = glyphs.length - 1;
    while (index >= 0) {
      if (glyphs[index].id === 0xffff) {
        glyphs.splice(index, 1);
      }

      index--;
    }

    return glyphs;
  };

  AATMorxProcessor.prototype.processSubtable = function processSubtable(subtable, glyphs) {
    this.subtable = subtable;
    this.glyphs = glyphs;
    if (this.subtable.type === 4) {
      this.processNoncontextualSubstitutions(this.subtable, this.glyphs);
      return;
    }

    this.ligatureStack = [];
    this.markedGlyph = null;
    this.firstGlyph = null;
    this.lastGlyph = null;
    this.markedIndex = null;

    var stateMachine = this.getStateMachine(subtable);
    var process = this.getProcessor();

    var reverse = !!(this.subtable.coverage & REVERSE_DIRECTION);
    return stateMachine.process(this.glyphs, reverse, process);
  };

  AATMorxProcessor.prototype.getStateMachine = function getStateMachine(subtable) {
    return new AATStateMachine(subtable.table.stateTable);
  };

  AATMorxProcessor.prototype.getProcessor = function getProcessor() {
    switch (this.subtable.type) {
      case 0:
        return this.processIndicRearragement;
      case 1:
        return this.processContextualSubstitution;
      case 2:
        return this.processLigature;
      case 4:
        return this.processNoncontextualSubstitutions;
      case 5:
        return this.processGlyphInsertion;
      default:
        throw new Error('Invalid morx subtable type: ' + this.subtable.type);
    }
  };

  AATMorxProcessor.prototype.processIndicRearragement = function processIndicRearragement(glyph, entry, index) {
    if (entry.flags & MARK_FIRST) {
      this.firstGlyph = index;
    }

    if (entry.flags & MARK_LAST) {
      this.lastGlyph = index;
    }

    reorderGlyphs(this.glyphs, entry.flags & VERB, this.firstGlyph, this.lastGlyph);
  };

  AATMorxProcessor.prototype.processContextualSubstitution = function processContextualSubstitution(glyph, entry, index) {
    var subsitutions = this.subtable.table.substitutionTable.items;
    if (entry.markIndex !== 0xffff) {
      var lookup = subsitutions.getItem(entry.markIndex);
      var lookupTable = new AATLookupTable(lookup);
      glyph = this.glyphs[this.markedGlyph];
      var gid = lookupTable.lookup(glyph.id);
      if (gid) {
        this.glyphs[this.markedGlyph] = this.font.getGlyph(gid, glyph.codePoints);
      }
    }

    if (entry.currentIndex !== 0xffff) {
      var _lookup = subsitutions.getItem(entry.currentIndex);
      var _lookupTable = new AATLookupTable(_lookup);
      glyph = this.glyphs[index];
      var gid = _lookupTable.lookup(glyph.id);
      if (gid) {
        this.glyphs[index] = this.font.getGlyph(gid, glyph.codePoints);
      }
    }

    if (entry.flags & SET_MARK) {
      this.markedGlyph = index;
    }
  };

  AATMorxProcessor.prototype.processLigature = function processLigature(glyph, entry, index) {
    if (entry.flags & SET_COMPONENT) {
      this.ligatureStack.push(index);
    }

    if (entry.flags & PERFORM_ACTION) {
      var _ligatureStack;

      var actions = this.subtable.table.ligatureActions;
      var components = this.subtable.table.components;
      var ligatureList = this.subtable.table.ligatureList;

      var actionIndex = entry.action;
      var last = false;
      var ligatureIndex = 0;
      var codePoints = [];
      var ligatureGlyphs = [];

      while (!last) {
        var _codePoints;

        var componentGlyph = this.ligatureStack.pop();
        (_codePoints = codePoints).unshift.apply(_codePoints, this.glyphs[componentGlyph].codePoints);

        var action = actions.getItem(actionIndex++);
        last = !!(action & LAST_MASK);
        var store = !!(action & STORE_MASK);
        var offset = (action & OFFSET_MASK) << 2 >> 2; // sign extend 30 to 32 bits
        offset += this.glyphs[componentGlyph].id;

        var component = components.getItem(offset);
        ligatureIndex += component;

        if (last || store) {
          var ligatureEntry = ligatureList.getItem(ligatureIndex);
          this.glyphs[componentGlyph] = this.font.getGlyph(ligatureEntry, codePoints);
          ligatureGlyphs.push(componentGlyph);
          ligatureIndex = 0;
          codePoints = [];
        } else {
          this.glyphs[componentGlyph] = this.font.getGlyph(0xffff);
        }
      }

      // Put ligature glyph indexes back on the stack
      (_ligatureStack = this.ligatureStack).push.apply(_ligatureStack, ligatureGlyphs);
    }
  };

  AATMorxProcessor.prototype.processNoncontextualSubstitutions = function processNoncontextualSubstitutions(subtable, glyphs, index) {
    var lookupTable = new AATLookupTable(subtable.table.lookupTable);

    for (index = 0; index < glyphs.length; index++) {
      var glyph = glyphs[index];
      if (glyph.id !== 0xffff) {
        var gid = lookupTable.lookup(glyph.id);
        if (gid) {
          // 0 means do nothing
          glyphs[index] = this.font.getGlyph(gid, glyph.codePoints);
        }
      }
    }
  };

  AATMorxProcessor.prototype._insertGlyphs = function _insertGlyphs(glyphIndex, insertionActionIndex, count, isBefore) {
    var _glyphs;

    var insertions = [];
    while (count--) {
      var gid = this.subtable.table.insertionActions.getItem(insertionActionIndex++);
      insertions.push(this.font.getGlyph(gid));
    }

    if (!isBefore) {
      glyphIndex++;
    }

    (_glyphs = this.glyphs).splice.apply(_glyphs, [glyphIndex, 0].concat(insertions));
  };

  AATMorxProcessor.prototype.processGlyphInsertion = function processGlyphInsertion(glyph, entry, index) {
    if (entry.flags & SET_MARK) {
      this.markedIndex = index;
    }

    if (entry.markedInsertIndex !== 0xffff) {
      var count = (entry.flags & MARKED_INSERT_COUNT) >>> 5;
      var isBefore = !!(entry.flags & MARKED_INSERT_BEFORE);
      this._insertGlyphs(this.markedIndex, entry.markedInsertIndex, count, isBefore);
    }

    if (entry.currentInsertIndex !== 0xffff) {
      var _count = (entry.flags & CURRENT_INSERT_COUNT) >>> 5;
      var _isBefore = !!(entry.flags & CURRENT_INSERT_BEFORE);
      this._insertGlyphs(index, entry.currentInsertIndex, _count, _isBefore);
    }
  };

  AATMorxProcessor.prototype.getSupportedFeatures = function getSupportedFeatures() {
    var features = [];
    for (var _iterator4 = this.morx.chains, _isArray4 = Array.isArray(_iterator4), _i4 = 0, _iterator4 = _isArray4 ? _iterator4 : _getIterator(_iterator4);;) {
      var _ref4;

      if (_isArray4) {
        if (_i4 >= _iterator4.length) break;
        _ref4 = _iterator4[_i4++];
      } else {
        _i4 = _iterator4.next();
        if (_i4.done) break;
        _ref4 = _i4.value;
      }

      var chain = _ref4;

      for (var _iterator5 = chain.features, _isArray5 = Array.isArray(_iterator5), _i5 = 0, _iterator5 = _isArray5 ? _iterator5 : _getIterator(_iterator5);;) {
        var _ref5;

        if (_isArray5) {
          if (_i5 >= _iterator5.length) break;
          _ref5 = _iterator5[_i5++];
        } else {
          _i5 = _iterator5.next();
          if (_i5.done) break;
          _ref5 = _i5.value;
        }

        var feature = _ref5;

        features.push([feature.featureType, feature.featureSetting]);
      }
    }

    return features;
  };

  AATMorxProcessor.prototype.generateInputs = function generateInputs(gid) {
    if (!this.inputCache) {
      this.generateInputCache();
    }

    return this.inputCache[gid] || [];
  };

  AATMorxProcessor.prototype.generateInputCache = function generateInputCache() {
    this.inputCache = {};

    for (var _iterator6 = this.morx.chains, _isArray6 = Array.isArray(_iterator6), _i6 = 0, _iterator6 = _isArray6 ? _iterator6 : _getIterator(_iterator6);;) {
      var _ref6;

      if (_isArray6) {
        if (_i6 >= _iterator6.length) break;
        _ref6 = _iterator6[_i6++];
      } else {
        _i6 = _iterator6.next();
        if (_i6.done) break;
        _ref6 = _i6.value;
      }

      var chain = _ref6;

      var flags = chain.defaultFlags;

      for (var _iterator7 = chain.subtables, _isArray7 = Array.isArray(_iterator7), _i7 = 0, _iterator7 = _isArray7 ? _iterator7 : _getIterator(_iterator7);;) {
        var _ref7;

        if (_isArray7) {
          if (_i7 >= _iterator7.length) break;
          _ref7 = _iterator7[_i7++];
        } else {
          _i7 = _iterator7.next();
          if (_i7.done) break;
          _ref7 = _i7.value;
        }

        var subtable = _ref7;

        if (subtable.subFeatureFlags & flags) {
          this.generateInputsForSubtable(subtable);
        }
      }
    }
  };

  AATMorxProcessor.prototype.generateInputsForSubtable = function generateInputsForSubtable(subtable) {
    var _this = this;

    // Currently, only supporting ligature subtables.
    if (subtable.type !== 2) {
      return;
    }

    var reverse = !!(subtable.coverage & REVERSE_DIRECTION);
    if (reverse) {
      throw new Error('Reverse subtable, not supported.');
    }

    this.subtable = subtable;
    this.ligatureStack = [];

    var stateMachine = this.getStateMachine(subtable);
    var process = this.getProcessor();

    var input = [];
    var stack = [];
    this.glyphs = [];

    stateMachine.traverse({
      enter: function enter(glyph, entry) {
        var glyphs = _this.glyphs;
        stack.push({
          glyphs: glyphs.slice(),
          ligatureStack: _this.ligatureStack.slice()
        });

        // Add glyph to input and glyphs to process.
        var g = _this.font.getGlyph(glyph);
        input.push(g);
        glyphs.push(input[input.length - 1]);

        // Process ligature substitution
        process(glyphs[glyphs.length - 1], entry, glyphs.length - 1);

        // Add input to result if only one matching (non-deleted) glyph remains.
        var count = 0;
        var found = 0;
        for (var i = 0; i < glyphs.length && count <= 1; i++) {
          if (glyphs[i].id !== 0xffff) {
            count++;
            found = glyphs[i].id;
          }
        }

        if (count === 1) {
          var result = input.map(function (g) {
            return g.id;
          });
          var _cache = _this.inputCache[found];
          if (_cache) {
            _cache.push(result);
          } else {
            _this.inputCache[found] = [result];
          }
        }
      },

      exit: function exit() {
        var _stack$pop = stack.pop();

        _this.glyphs = _stack$pop.glyphs;
        _this.ligatureStack = _stack$pop.ligatureStack;

        input.pop();
      }
    });
  };

  return AATMorxProcessor;
}(), (_applyDecoratedDescriptor$2(_class$2.prototype, 'getStateMachine', [cache], _Object$getOwnPropertyDescriptor(_class$2.prototype, 'getStateMachine'), _class$2.prototype)), _class$2);

function swap(glyphs, rangeA, rangeB) {
  var reverseA = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  var reverseB = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;

  var end = glyphs.splice(rangeB[0] - (rangeB[1] - 1), rangeB[1]);
  if (reverseB) {
    end.reverse();
  }

  var start = glyphs.splice.apply(glyphs, [rangeA[0], rangeA[1]].concat(end));
  if (reverseA) {
    start.reverse();
  }

  glyphs.splice.apply(glyphs, [rangeB[0] - (rangeA[1] - 1), 0].concat(start));
  return glyphs;
}

function reorderGlyphs(glyphs, verb, firstGlyph, lastGlyph) {
  var length = lastGlyph - firstGlyph + 1;
  switch (verb) {
    case 0:
      // no change
      return glyphs;

    case 1:
      // Ax => xA
      return swap(glyphs, [firstGlyph, 1], [lastGlyph, 0]);

    case 2:
      // xD => Dx
      return swap(glyphs, [firstGlyph, 0], [lastGlyph, 1]);

    case 3:
      // AxD => DxA
      return swap(glyphs, [firstGlyph, 1], [lastGlyph, 1]);

    case 4:
      // ABx => xAB
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 0]);

    case 5:
      // ABx => xBA
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 0], true, false);

    case 6:
      // xCD => CDx
      return swap(glyphs, [firstGlyph, 0], [lastGlyph, 2]);

    case 7:
      // xCD => DCx
      return swap(glyphs, [firstGlyph, 0], [lastGlyph, 2], false, true);

    case 8:
      // AxCD => CDxA
      return swap(glyphs, [firstGlyph, 1], [lastGlyph, 2]);

    case 9:
      // AxCD => DCxA
      return swap(glyphs, [firstGlyph, 1], [lastGlyph, 2], false, true);

    case 10:
      // ABxD => DxAB
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 1]);

    case 11:
      // ABxD => DxBA
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 1], true, false);

    case 12:
      // ABxCD => CDxAB
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 2]);

    case 13:
      // ABxCD => CDxBA
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 2], true, false);

    case 14:
      // ABxCD => DCxAB
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 2], false, true);

    case 15:
      // ABxCD => DCxBA
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 2], true, true);

    default:
      throw new Error('Unknown verb: ' + verb);
  }
}

var AATLayoutEngine = function () {
  function AATLayoutEngine(font) {
    _classCallCheck(this, AATLayoutEngine);

    this.font = font;
    this.morxProcessor = new AATMorxProcessor(font);
    this.fallbackPosition = false;
  }

  AATLayoutEngine.prototype.substitute = function substitute(glyphRun) {
    // AAT expects the glyphs to be in visual order prior to morx processing,
    // so reverse the glyphs if the script is right-to-left.
    if (glyphRun.direction === 'rtl') {
      glyphRun.glyphs.reverse();
    }

    this.morxProcessor.process(glyphRun.glyphs, mapOTToAAT(glyphRun.features));
  };

  AATLayoutEngine.prototype.getAvailableFeatures = function getAvailableFeatures(script, language) {
    return mapAATToOT(this.morxProcessor.getSupportedFeatures());
  };

  AATLayoutEngine.prototype.stringsForGlyph = function stringsForGlyph(gid) {
    var glyphStrings = this.morxProcessor.generateInputs(gid);
    var result = new _Set();

    for (var _iterator = glyphStrings, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var glyphs = _ref;

      this._addStrings(glyphs, 0, result, '');
    }

    return result;
  };

  AATLayoutEngine.prototype._addStrings = function _addStrings(glyphs, index, strings, string) {
    var codePoints = this.font._cmapProcessor.codePointsForGlyph(glyphs[index]);

    for (var _iterator2 = codePoints, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
      var _ref2;

      if (_isArray2) {
        if (_i2 >= _iterator2.length) break;
        _ref2 = _iterator2[_i2++];
      } else {
        _i2 = _iterator2.next();
        if (_i2.done) break;
        _ref2 = _i2.value;
      }

      var codePoint = _ref2;

      var s = string + _String$fromCodePoint(codePoint);
      if (index < glyphs.length - 1) {
        this._addStrings(glyphs, index + 1, strings, s);
      } else {
        strings.add(s);
      }
    }
  };

  return AATLayoutEngine;
}();

/**
 * ShapingPlans are used by the OpenType shapers to store which
 * features should by applied, and in what order to apply them.
 * The features are applied in groups called stages. A feature
 * can be applied globally to all glyphs, or locally to only
 * specific glyphs.
 *
 * @private
 */

var ShapingPlan = function () {
  function ShapingPlan(font, script, direction) {
    _classCallCheck(this, ShapingPlan);

    this.font = font;
    this.script = script;
    this.direction = direction;
    this.stages = [];
    this.globalFeatures = {};
    this.allFeatures = {};
  }

  /**
   * Adds the given features to the last stage.
   * Ignores features that have already been applied.
   */


  ShapingPlan.prototype._addFeatures = function _addFeatures(features, global) {
    var stageIndex = this.stages.length - 1;
    var stage = this.stages[stageIndex];
    for (var _iterator = features, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var feature = _ref;

      if (this.allFeatures[feature] == null) {
        stage.push(feature);
        this.allFeatures[feature] = stageIndex;

        if (global) {
          this.globalFeatures[feature] = true;
        }
      }
    }
  };

  /**
   * Add features to the last stage
   */


  ShapingPlan.prototype.add = function add(arg) {
    var global = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

    if (this.stages.length === 0) {
      this.stages.push([]);
    }

    if (typeof arg === 'string') {
      arg = [arg];
    }

    if (Array.isArray(arg)) {
      this._addFeatures(arg, global);
    } else if ((typeof arg === 'undefined' ? 'undefined' : _typeof(arg)) === 'object') {
      this._addFeatures(arg.global || [], true);
      this._addFeatures(arg.local || [], false);
    } else {
      throw new Error("Unsupported argument to ShapingPlan#add");
    }
  };

  /**
   * Add a new stage
   */


  ShapingPlan.prototype.addStage = function addStage(arg, global) {
    if (typeof arg === 'function') {
      this.stages.push(arg, []);
    } else {
      this.stages.push([]);
      this.add(arg, global);
    }
  };

  ShapingPlan.prototype.setFeatureOverrides = function setFeatureOverrides(features) {
    if (Array.isArray(features)) {
      this.add(features);
    } else if ((typeof features === 'undefined' ? 'undefined' : _typeof(features)) === 'object') {
      for (var tag in features) {
        if (features[tag]) {
          this.add(tag);
        } else if (this.allFeatures[tag] != null) {
          var stage = this.stages[this.allFeatures[tag]];
          stage.splice(stage.indexOf(tag), 1);
          delete this.allFeatures[tag];
          delete this.globalFeatures[tag];
        }
      }
    }
  };

  /**
   * Assigns the global features to the given glyphs
   */


  ShapingPlan.prototype.assignGlobalFeatures = function assignGlobalFeatures(glyphs) {
    for (var _iterator2 = glyphs, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
      var _ref2;

      if (_isArray2) {
        if (_i2 >= _iterator2.length) break;
        _ref2 = _iterator2[_i2++];
      } else {
        _i2 = _iterator2.next();
        if (_i2.done) break;
        _ref2 = _i2.value;
      }

      var glyph = _ref2;

      for (var feature in this.globalFeatures) {
        glyph.features[feature] = true;
      }
    }
  };

  /**
   * Executes the planned stages using the given OTProcessor
   */


  ShapingPlan.prototype.process = function process(processor, glyphs, positions) {
    for (var _iterator3 = this.stages, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
      var _ref3;

      if (_isArray3) {
        if (_i3 >= _iterator3.length) break;
        _ref3 = _iterator3[_i3++];
      } else {
        _i3 = _iterator3.next();
        if (_i3.done) break;
        _ref3 = _i3.value;
      }

      var stage = _ref3;

      if (typeof stage === 'function') {
        if (!positions) {
          stage(this.font, glyphs, this);
        }
      } else if (stage.length > 0) {
        processor.applyFeatures(stage, glyphs, positions);
      }
    }
  };

  return ShapingPlan;
}();

var _class$4;
var _temp;
var VARIATION_FEATURES = ['rvrn'];
var COMMON_FEATURES = ['ccmp', 'locl', 'rlig', 'mark', 'mkmk'];
var FRACTIONAL_FEATURES = ['frac', 'numr', 'dnom'];
var HORIZONTAL_FEATURES = ['calt', 'clig', 'liga', 'rclt', 'curs', 'kern'];
var DIRECTIONAL_FEATURES = {
  ltr: ['ltra', 'ltrm'],
  rtl: ['rtla', 'rtlm']
};

var DefaultShaper = (_temp = _class$4 = function () {
  function DefaultShaper() {
    _classCallCheck(this, DefaultShaper);
  }

  DefaultShaper.plan = function plan(_plan, glyphs, features) {
    // Plan the features we want to apply
    this.planPreprocessing(_plan);
    this.planFeatures(_plan);
    this.planPostprocessing(_plan, features);

    // Assign the global features to all the glyphs
    _plan.assignGlobalFeatures(glyphs);

    // Assign local features to glyphs
    this.assignFeatures(_plan, glyphs);
  };

  DefaultShaper.planPreprocessing = function planPreprocessing(plan) {
    plan.add({
      global: [].concat(VARIATION_FEATURES, DIRECTIONAL_FEATURES[plan.direction]),
      local: FRACTIONAL_FEATURES
    });
  };

  DefaultShaper.planFeatures = function planFeatures(plan) {
    // Do nothing by default. Let subclasses override this.
  };

  DefaultShaper.planPostprocessing = function planPostprocessing(plan, userFeatures) {
    plan.add([].concat(COMMON_FEATURES, HORIZONTAL_FEATURES));
    plan.setFeatureOverrides(userFeatures);
  };

  DefaultShaper.assignFeatures = function assignFeatures(plan, glyphs) {
    // Enable contextual fractions
    for (var i = 0; i < glyphs.length; i++) {
      var glyph = glyphs[i];
      if (glyph.codePoints[0] === 0x2044) {
        // fraction slash
        var start = i;
        var end = i + 1;

        // Apply numerator
        while (start > 0 && unicode.isDigit(glyphs[start - 1].codePoints[0])) {
          glyphs[start - 1].features.numr = true;
          glyphs[start - 1].features.frac = true;
          start--;
        }

        // Apply denominator
        while (end < glyphs.length && unicode.isDigit(glyphs[end].codePoints[0])) {
          glyphs[end].features.dnom = true;
          glyphs[end].features.frac = true;
          end++;
        }

        // Apply fraction slash
        glyph.features.frac = true;
        i = end - 1;
      }
    }
  };

  return DefaultShaper;
}(), _class$4.zeroMarkWidths = 'AFTER_GPOS', _temp);

var trie = new UnicodeTrie(Buffer("AAHwAAAAAAAAADgAAf0BAv7tmi1MxDAUx7vtvjhAgcDgkEgEAnmXEBIMCYaEcygEiqBQ4FAkCE4ikUgMiiBJSAgSiUQSDMn9L9eSl6bddddug9t7yS/trevre+3r27pcNxZiG+yCfdCVv/9LeQxOwRm4AJegD27ALbgD9+ABPJF+z+BN/h7yDj5k/VOWX6SdmU5+wLWknggxDxaS8u0qiiX4uiz9XamQ3wzDMAzDMAzDMAzDVI/h959V/v7BMAzDMAzDMLlyNTNiMSdewVxbiA44B4/guz1qW58VYlMI0WsJ0W+N6kXw0spvPtdwhtkwnGM6uLaV4Xyzg3v3PM9DPfQ/sOg4xPWjipy31P8LTqbU304c/cLCUmWJLNB2Uz2U1KTeRKNmKHVMfbJC+/0loTZRH/W5cvEvBJPMbREkWt3FD1NcqXZBSpuE2Ad0PBehPtNrPtIEdYP+hiRt/V1jIiE69X4NT/uVZI3PUHE9bm5M7ePGdZWy951v7Nn6j8v1WWKP3mt6ttnsigx6VN7Vc0VomSSGqW2mGNP1muZPl7LfjNUaKNFtDGVf2fvE9O7VlBS5j333c5p/eeoOqcs1R/hIqDWLJ7TTlksirVT1SI7l8k4Yp+g3jafGcrU1RM6l9th80XOpnlN97bDNY4i4s61B0Si/ipa0uHMl6zqEjlFfCZm/TM8KmzQDjmuTAQ==","base64"));
var FEATURES = ['isol', 'fina', 'fin2', 'fin3', 'medi', 'med2', 'init'];

var ShapingClasses = {
  Non_Joining: 0,
  Left_Joining: 1,
  Right_Joining: 2,
  Dual_Joining: 3,
  Join_Causing: 3,
  ALAPH: 4,
  'DALATH RISH': 5,
  Transparent: 6
};

var ISOL = 'isol';
var FINA = 'fina';
var FIN2 = 'fin2';
var FIN3 = 'fin3';
var MEDI = 'medi';
var MED2 = 'med2';
var INIT = 'init';
var NONE = null;

// Each entry is [prevAction, curAction, nextState]
var STATE_TABLE = [
//   Non_Joining,        Left_Joining,       Right_Joining,     Dual_Joining,           ALAPH,            DALATH RISH
// State 0: prev was U,  not willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [NONE, ISOL, 1], [NONE, ISOL, 2], [NONE, ISOL, 1], [NONE, ISOL, 6]],

// State 1: prev was R or ISOL/ALAPH,  not willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [NONE, ISOL, 1], [NONE, ISOL, 2], [NONE, FIN2, 5], [NONE, ISOL, 6]],

// State 2: prev was D/L in ISOL form,  willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [INIT, FINA, 1], [INIT, FINA, 3], [INIT, FINA, 4], [INIT, FINA, 6]],

// State 3: prev was D in FINA form,  willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [MEDI, FINA, 1], [MEDI, FINA, 3], [MEDI, FINA, 4], [MEDI, FINA, 6]],

// State 4: prev was FINA ALAPH,  not willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [MED2, ISOL, 1], [MED2, ISOL, 2], [MED2, FIN2, 5], [MED2, ISOL, 6]],

// State 5: prev was FIN2/FIN3 ALAPH,  not willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [ISOL, ISOL, 1], [ISOL, ISOL, 2], [ISOL, FIN2, 5], [ISOL, ISOL, 6]],

// State 6: prev was DALATH/RISH,  not willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [NONE, ISOL, 1], [NONE, ISOL, 2], [NONE, FIN3, 5], [NONE, ISOL, 6]]];

/**
 * This is a shaper for Arabic, and other cursive scripts.
 * It uses data from ArabicShaping.txt in the Unicode database,
 * compiled to a UnicodeTrie by generate-data.coffee.
 *
 * The shaping state machine was ported from Harfbuzz.
 * https://github.com/behdad/harfbuzz/blob/master/src/hb-ot-shape-complex-arabic.cc
 */

var ArabicShaper = function (_DefaultShaper) {
  _inherits(ArabicShaper, _DefaultShaper);

  function ArabicShaper() {
    _classCallCheck(this, ArabicShaper);

    return _possibleConstructorReturn(this, _DefaultShaper.apply(this, arguments));
  }

  ArabicShaper.planFeatures = function planFeatures(plan) {
    plan.add(['ccmp', 'locl']);
    for (var i = 0; i < FEATURES.length; i++) {
      var feature = FEATURES[i];
      plan.addStage(feature, false);
    }

    plan.addStage('mset');
  };

  ArabicShaper.assignFeatures = function assignFeatures(plan, glyphs) {
    _DefaultShaper.assignFeatures.call(this, plan, glyphs);

    var prev = -1;
    var state = 0;
    var actions = [];

    // Apply the state machine to map glyphs to features
    for (var i = 0; i < glyphs.length; i++) {
      var curAction = void 0,
          prevAction = void 0;
      var glyph = glyphs[i];
      var type = getShapingClass(glyph.codePoints[0]);
      if (type === ShapingClasses.Transparent) {
        actions[i] = NONE;
        continue;
      }

      var _STATE_TABLE$state$ty = STATE_TABLE[state][type];
      prevAction = _STATE_TABLE$state$ty[0];
      curAction = _STATE_TABLE$state$ty[1];
      state = _STATE_TABLE$state$ty[2];


      if (prevAction !== NONE && prev !== -1) {
        actions[prev] = prevAction;
      }

      actions[i] = curAction;
      prev = i;
    }

    // Apply the chosen features to their respective glyphs
    for (var index = 0; index < glyphs.length; index++) {
      var feature = void 0;
      var glyph = glyphs[index];
      if (feature = actions[index]) {
        glyph.features[feature] = true;
      }
    }
  };

  return ArabicShaper;
}(DefaultShaper);

function getShapingClass(codePoint) {
  var res = trie.get(codePoint);
  if (res) {
    return res - 1;
  }

  var category = unicode.getCategory(codePoint);
  if (category === 'Mn' || category === 'Me' || category === 'Cf') {
    return ShapingClasses.Transparent;
  }

  return ShapingClasses.Non_Joining;
}

var GlyphIterator = function () {
  function GlyphIterator(glyphs, options) {
    _classCallCheck(this, GlyphIterator);

    this.glyphs = glyphs;
    this.reset(options);
  }

  GlyphIterator.prototype.reset = function reset() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var index = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

    this.options = options;
    this.flags = options.flags || {};
    this.markAttachmentType = options.markAttachmentType || 0;
    this.index = index;
  };

  GlyphIterator.prototype.shouldIgnore = function shouldIgnore(glyph) {
    return this.flags.ignoreMarks && glyph.isMark || this.flags.ignoreBaseGlyphs && glyph.isBase || this.flags.ignoreLigatures && glyph.isLigature || this.markAttachmentType && glyph.isMark && glyph.markAttachmentType !== this.markAttachmentType;
  };

  GlyphIterator.prototype.move = function move(dir) {
    this.index += dir;
    while (0 <= this.index && this.index < this.glyphs.length && this.shouldIgnore(this.glyphs[this.index])) {
      this.index += dir;
    }

    if (0 > this.index || this.index >= this.glyphs.length) {
      return null;
    }

    return this.glyphs[this.index];
  };

  GlyphIterator.prototype.next = function next() {
    return this.move(+1);
  };

  GlyphIterator.prototype.prev = function prev() {
    return this.move(-1);
  };

  GlyphIterator.prototype.peek = function peek() {
    var count = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;

    var idx = this.index;
    var res = this.increment(count);
    this.index = idx;
    return res;
  };

  GlyphIterator.prototype.peekIndex = function peekIndex() {
    var count = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;

    var idx = this.index;
    this.increment(count);
    var res = this.index;
    this.index = idx;
    return res;
  };

  GlyphIterator.prototype.increment = function increment() {
    var count = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;

    var dir = count < 0 ? -1 : 1;
    count = Math.abs(count);
    while (count--) {
      this.move(dir);
    }

    return this.glyphs[this.index];
  };

  _createClass(GlyphIterator, [{
    key: "cur",
    get: function get() {
      return this.glyphs[this.index] || null;
    }
  }]);

  return GlyphIterator;
}();

var DEFAULT_SCRIPTS = ['DFLT', 'dflt', 'latn'];

var OTProcessor = function () {
  function OTProcessor(font, table) {
    _classCallCheck(this, OTProcessor);

    this.font = font;
    this.table = table;

    this.script = null;
    this.scriptTag = null;

    this.language = null;
    this.languageTag = null;

    this.features = {};
    this.lookups = {};

    // Setup variation substitutions
    this.variationsIndex = font._variationProcessor ? this.findVariationsIndex(font._variationProcessor.normalizedCoords) : -1;

    // initialize to default script + language
    this.selectScript();

    // current context (set by applyFeatures)
    this.glyphs = [];
    this.positions = []; // only used by GPOS
    this.ligatureID = 1;
    this.currentFeature = null;
  }

  OTProcessor.prototype.findScript = function findScript(script) {
    if (this.table.scriptList == null) {
      return null;
    }

    if (!Array.isArray(script)) {
      script = [script];
    }

    for (var _iterator = script, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var s = _ref;

      for (var _iterator2 = this.table.scriptList, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
        var _ref2;

        if (_isArray2) {
          if (_i2 >= _iterator2.length) break;
          _ref2 = _iterator2[_i2++];
        } else {
          _i2 = _iterator2.next();
          if (_i2.done) break;
          _ref2 = _i2.value;
        }

        var entry = _ref2;

        if (entry.tag === s) {
          return entry;
        }
      }
    }

    return null;
  };

  OTProcessor.prototype.selectScript = function selectScript(script, language, direction$$) {
    var changed = false;
    var entry = void 0;
    if (!this.script || script !== this.scriptTag) {
      entry = this.findScript(script);
      if (!entry) {
        entry = this.findScript(DEFAULT_SCRIPTS);
      }

      if (!entry) {
        return this.scriptTag;
      }

      this.scriptTag = entry.tag;
      this.script = entry.script;
      this.language = null;
      this.languageTag = null;
      changed = true;
    }

    if (!direction$$ || direction$$ !== this.direction) {
      this.direction = direction$$ || direction(script);
    }

    if (language && language.length < 4) {
      language += ' '.repeat(4 - language.length);
    }

    if (!language || language !== this.languageTag) {
      this.language = null;

      for (var _iterator3 = this.script.langSysRecords, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
        var _ref3;

        if (_isArray3) {
          if (_i3 >= _iterator3.length) break;
          _ref3 = _iterator3[_i3++];
        } else {
          _i3 = _iterator3.next();
          if (_i3.done) break;
          _ref3 = _i3.value;
        }

        var lang = _ref3;

        if (lang.tag === language) {
          this.language = lang.langSys;
          this.languageTag = lang.tag;
          break;
        }
      }

      if (!this.language) {
        this.language = this.script.defaultLangSys;
        this.languageTag = null;
      }

      changed = true;
    }

    // Build a feature lookup table
    if (changed) {
      this.features = {};
      if (this.language) {
        for (var _iterator4 = this.language.featureIndexes, _isArray4 = Array.isArray(_iterator4), _i4 = 0, _iterator4 = _isArray4 ? _iterator4 : _getIterator(_iterator4);;) {
          var _ref4;

          if (_isArray4) {
            if (_i4 >= _iterator4.length) break;
            _ref4 = _iterator4[_i4++];
          } else {
            _i4 = _iterator4.next();
            if (_i4.done) break;
            _ref4 = _i4.value;
          }

          var featureIndex = _ref4;

          var record = this.table.featureList[featureIndex];
          var substituteFeature = this.substituteFeatureForVariations(featureIndex);
          this.features[record.tag] = substituteFeature || record.feature;
        }
      }
    }

    return this.scriptTag;
  };

  OTProcessor.prototype.lookupsForFeatures = function lookupsForFeatures() {
    var userFeatures = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    var exclude = arguments[1];

    var lookups = [];
    for (var _iterator5 = userFeatures, _isArray5 = Array.isArray(_iterator5), _i5 = 0, _iterator5 = _isArray5 ? _iterator5 : _getIterator(_iterator5);;) {
      var _ref5;

      if (_isArray5) {
        if (_i5 >= _iterator5.length) break;
        _ref5 = _iterator5[_i5++];
      } else {
        _i5 = _iterator5.next();
        if (_i5.done) break;
        _ref5 = _i5.value;
      }

      var tag = _ref5;

      var feature = this.features[tag];
      if (!feature) {
        continue;
      }

      for (var _iterator6 = feature.lookupListIndexes, _isArray6 = Array.isArray(_iterator6), _i6 = 0, _iterator6 = _isArray6 ? _iterator6 : _getIterator(_iterator6);;) {
        var _ref6;

        if (_isArray6) {
          if (_i6 >= _iterator6.length) break;
          _ref6 = _iterator6[_i6++];
        } else {
          _i6 = _iterator6.next();
          if (_i6.done) break;
          _ref6 = _i6.value;
        }

        var lookupIndex = _ref6;

        if (exclude && exclude.indexOf(lookupIndex) !== -1) {
          continue;
        }

        lookups.push({
          feature: tag,
          index: lookupIndex,
          lookup: this.table.lookupList.get(lookupIndex)
        });
      }
    }

    lookups.sort(function (a, b) {
      return a.index - b.index;
    });
    return lookups;
  };

  OTProcessor.prototype.substituteFeatureForVariations = function substituteFeatureForVariations(featureIndex) {
    if (this.variationsIndex === -1) {
      return null;
    }

    var record = this.table.featureVariations.featureVariationRecords[this.variationsIndex];
    var substitutions = record.featureTableSubstitution.substitutions;
    for (var _iterator7 = substitutions, _isArray7 = Array.isArray(_iterator7), _i7 = 0, _iterator7 = _isArray7 ? _iterator7 : _getIterator(_iterator7);;) {
      var _ref7;

      if (_isArray7) {
        if (_i7 >= _iterator7.length) break;
        _ref7 = _iterator7[_i7++];
      } else {
        _i7 = _iterator7.next();
        if (_i7.done) break;
        _ref7 = _i7.value;
      }

      var substitution = _ref7;

      if (substitution.featureIndex === featureIndex) {
        return substitution.alternateFeatureTable;
      }
    }

    return null;
  };

  OTProcessor.prototype.findVariationsIndex = function findVariationsIndex(coords) {
    var variations = this.table.featureVariations;
    if (!variations) {
      return -1;
    }

    var records = variations.featureVariationRecords;
    for (var i = 0; i < records.length; i++) {
      var conditions = records[i].conditionSet.conditionTable;
      if (this.variationConditionsMatch(conditions, coords)) {
        return i;
      }
    }

    return -1;
  };

  OTProcessor.prototype.variationConditionsMatch = function variationConditionsMatch(conditions, coords) {
    return conditions.every(function (condition) {
      var coord = condition.axisIndex < coords.length ? coords[condition.axisIndex] : 0;
      return condition.filterRangeMinValue <= coord && coord <= condition.filterRangeMaxValue;
    });
  };

  OTProcessor.prototype.applyFeatures = function applyFeatures(userFeatures, glyphs, advances) {
    var lookups = this.lookupsForFeatures(userFeatures);
    this.applyLookups(lookups, glyphs, advances);
  };

  OTProcessor.prototype.applyLookups = function applyLookups(lookups, glyphs, positions) {
    this.glyphs = glyphs;
    this.positions = positions;
    this.glyphIterator = new GlyphIterator(glyphs);

    for (var _iterator8 = lookups, _isArray8 = Array.isArray(_iterator8), _i8 = 0, _iterator8 = _isArray8 ? _iterator8 : _getIterator(_iterator8);;) {
      var _ref8;

      if (_isArray8) {
        if (_i8 >= _iterator8.length) break;
        _ref8 = _iterator8[_i8++];
      } else {
        _i8 = _iterator8.next();
        if (_i8.done) break;
        _ref8 = _i8.value;
      }

      var _ref9 = _ref8,
          feature = _ref9.feature,
          lookup = _ref9.lookup;

      this.currentFeature = feature;
      this.glyphIterator.reset(lookup.flags);

      while (this.glyphIterator.index < glyphs.length) {
        if (!(feature in this.glyphIterator.cur.features)) {
          this.glyphIterator.next();
          continue;
        }

        for (var _iterator9 = lookup.subTables, _isArray9 = Array.isArray(_iterator9), _i9 = 0, _iterator9 = _isArray9 ? _iterator9 : _getIterator(_iterator9);;) {
          var _ref10;

          if (_isArray9) {
            if (_i9 >= _iterator9.length) break;
            _ref10 = _iterator9[_i9++];
          } else {
            _i9 = _iterator9.next();
            if (_i9.done) break;
            _ref10 = _i9.value;
          }

          var table = _ref10;

          var res = this.applyLookup(lookup.lookupType, table);
          if (res) {
            break;
          }
        }

        this.glyphIterator.next();
      }
    }
  };

  OTProcessor.prototype.applyLookup = function applyLookup(lookup, table) {
    throw new Error("applyLookup must be implemented by subclasses");
  };

  OTProcessor.prototype.applyLookupList = function applyLookupList(lookupRecords) {
    var options = this.glyphIterator.options;
    var glyphIndex = this.glyphIterator.index;

    for (var _iterator10 = lookupRecords, _isArray10 = Array.isArray(_iterator10), _i10 = 0, _iterator10 = _isArray10 ? _iterator10 : _getIterator(_iterator10);;) {
      var _ref11;

      if (_isArray10) {
        if (_i10 >= _iterator10.length) break;
        _ref11 = _iterator10[_i10++];
      } else {
        _i10 = _iterator10.next();
        if (_i10.done) break;
        _ref11 = _i10.value;
      }

      var lookupRecord = _ref11;

      // Reset flags and find glyph index for this lookup record
      this.glyphIterator.reset(options, glyphIndex);
      this.glyphIterator.increment(lookupRecord.sequenceIndex);

      // Get the lookup and setup flags for subtables
      var lookup = this.table.lookupList.get(lookupRecord.lookupListIndex);
      this.glyphIterator.reset(lookup.flags, this.glyphIterator.index);

      // Apply lookup subtables until one matches
      for (var _iterator11 = lookup.subTables, _isArray11 = Array.isArray(_iterator11), _i11 = 0, _iterator11 = _isArray11 ? _iterator11 : _getIterator(_iterator11);;) {
        var _ref12;

        if (_isArray11) {
          if (_i11 >= _iterator11.length) break;
          _ref12 = _iterator11[_i11++];
        } else {
          _i11 = _iterator11.next();
          if (_i11.done) break;
          _ref12 = _i11.value;
        }

        var table = _ref12;

        if (this.applyLookup(lookup.lookupType, table)) {
          break;
        }
      }
    }

    this.glyphIterator.reset(options, glyphIndex);
    return true;
  };

  OTProcessor.prototype.coverageIndex = function coverageIndex(coverage, glyph) {
    if (glyph == null) {
      glyph = this.glyphIterator.cur.id;
    }

    switch (coverage.version) {
      case 1:
        return coverage.glyphs.indexOf(glyph);

      case 2:
        for (var _iterator12 = coverage.rangeRecords, _isArray12 = Array.isArray(_iterator12), _i12 = 0, _iterator12 = _isArray12 ? _iterator12 : _getIterator(_iterator12);;) {
          var _ref13;

          if (_isArray12) {
            if (_i12 >= _iterator12.length) break;
            _ref13 = _iterator12[_i12++];
          } else {
            _i12 = _iterator12.next();
            if (_i12.done) break;
            _ref13 = _i12.value;
          }

          var range = _ref13;

          if (range.start <= glyph && glyph <= range.end) {
            return range.startCoverageIndex + glyph - range.start;
          }
        }

        break;
    }

    return -1;
  };

  OTProcessor.prototype.match = function match(sequenceIndex, sequence, fn, matched) {
    var pos = this.glyphIterator.index;
    var glyph = this.glyphIterator.increment(sequenceIndex);
    var idx = 0;

    while (idx < sequence.length && glyph && fn(sequence[idx], glyph)) {
      if (matched) {
        matched.push(this.glyphIterator.index);
      }

      idx++;
      glyph = this.glyphIterator.next();
    }

    this.glyphIterator.index = pos;
    if (idx < sequence.length) {
      return false;
    }

    return matched || true;
  };

  OTProcessor.prototype.sequenceMatches = function sequenceMatches(sequenceIndex, sequence) {
    return this.match(sequenceIndex, sequence, function (component, glyph) {
      return component === glyph.id;
    });
  };

  OTProcessor.prototype.sequenceMatchIndices = function sequenceMatchIndices(sequenceIndex, sequence) {
    var _this = this;

    return this.match(sequenceIndex, sequence, function (component, glyph) {
      // If the current feature doesn't apply to this glyph,
      if (!(_this.currentFeature in glyph.features)) {
        return false;
      }

      return component === glyph.id;
    }, []);
  };

  OTProcessor.prototype.coverageSequenceMatches = function coverageSequenceMatches(sequenceIndex, sequence) {
    var _this2 = this;

    return this.match(sequenceIndex, sequence, function (coverage, glyph) {
      return _this2.coverageIndex(coverage, glyph.id) >= 0;
    });
  };

  OTProcessor.prototype.getClassID = function getClassID(glyph, classDef) {
    switch (classDef.version) {
      case 1:
        // Class array
        var i = glyph - classDef.startGlyph;
        if (i >= 0 && i < classDef.classValueArray.length) {
          return classDef.classValueArray[i];
        }

        break;

      case 2:
        for (var _iterator13 = classDef.classRangeRecord, _isArray13 = Array.isArray(_iterator13), _i13 = 0, _iterator13 = _isArray13 ? _iterator13 : _getIterator(_iterator13);;) {
          var _ref14;

          if (_isArray13) {
            if (_i13 >= _iterator13.length) break;
            _ref14 = _iterator13[_i13++];
          } else {
            _i13 = _iterator13.next();
            if (_i13.done) break;
            _ref14 = _i13.value;
          }

          var range = _ref14;

          if (range.start <= glyph && glyph <= range.end) {
            return range.class;
          }
        }

        break;
    }

    return 0;
  };

  OTProcessor.prototype.classSequenceMatches = function classSequenceMatches(sequenceIndex, sequence, classDef) {
    var _this3 = this;

    return this.match(sequenceIndex, sequence, function (classID, glyph) {
      return classID === _this3.getClassID(glyph.id, classDef);
    });
  };

  OTProcessor.prototype.applyContext = function applyContext(table) {
    switch (table.version) {
      case 1:
        var index = this.coverageIndex(table.coverage);
        if (index === -1) {
          return false;
        }

        var set = table.ruleSets[index];
        for (var _iterator14 = set, _isArray14 = Array.isArray(_iterator14), _i14 = 0, _iterator14 = _isArray14 ? _iterator14 : _getIterator(_iterator14);;) {
          var _ref15;

          if (_isArray14) {
            if (_i14 >= _iterator14.length) break;
            _ref15 = _iterator14[_i14++];
          } else {
            _i14 = _iterator14.next();
            if (_i14.done) break;
            _ref15 = _i14.value;
          }

          var rule = _ref15;

          if (this.sequenceMatches(1, rule.input)) {
            return this.applyLookupList(rule.lookupRecords);
          }
        }

        break;

      case 2:
        if (this.coverageIndex(table.coverage) === -1) {
          return false;
        }

        index = this.getClassID(this.glyphIterator.cur.id, table.classDef);
        if (index === -1) {
          return false;
        }

        set = table.classSet[index];
        for (var _iterator15 = set, _isArray15 = Array.isArray(_iterator15), _i15 = 0, _iterator15 = _isArray15 ? _iterator15 : _getIterator(_iterator15);;) {
          var _ref16;

          if (_isArray15) {
            if (_i15 >= _iterator15.length) break;
            _ref16 = _iterator15[_i15++];
          } else {
            _i15 = _iterator15.next();
            if (_i15.done) break;
            _ref16 = _i15.value;
          }

          var _rule = _ref16;

          if (this.classSequenceMatches(1, _rule.classes, table.classDef)) {
            return this.applyLookupList(_rule.lookupRecords);
          }
        }

        break;

      case 3:
        if (this.coverageSequenceMatches(0, table.coverages)) {
          return this.applyLookupList(table.lookupRecords);
        }

        break;
    }

    return false;
  };

  OTProcessor.prototype.applyChainingContext = function applyChainingContext(table) {
    switch (table.version) {
      case 1:
        var index = this.coverageIndex(table.coverage);
        if (index === -1) {
          return false;
        }

        var set = table.chainRuleSets[index];
        for (var _iterator16 = set, _isArray16 = Array.isArray(_iterator16), _i16 = 0, _iterator16 = _isArray16 ? _iterator16 : _getIterator(_iterator16);;) {
          var _ref17;

          if (_isArray16) {
            if (_i16 >= _iterator16.length) break;
            _ref17 = _iterator16[_i16++];
          } else {
            _i16 = _iterator16.next();
            if (_i16.done) break;
            _ref17 = _i16.value;
          }

          var rule = _ref17;

          if (this.sequenceMatches(-rule.backtrack.length, rule.backtrack) && this.sequenceMatches(1, rule.input) && this.sequenceMatches(1 + rule.input.length, rule.lookahead)) {
            return this.applyLookupList(rule.lookupRecords);
          }
        }

        break;

      case 2:
        if (this.coverageIndex(table.coverage) === -1) {
          return false;
        }

        index = this.getClassID(this.glyphIterator.cur.id, table.inputClassDef);
        var rules = table.chainClassSet[index];
        if (!rules) {
          return false;
        }

        for (var _iterator17 = rules, _isArray17 = Array.isArray(_iterator17), _i17 = 0, _iterator17 = _isArray17 ? _iterator17 : _getIterator(_iterator17);;) {
          var _ref18;

          if (_isArray17) {
            if (_i17 >= _iterator17.length) break;
            _ref18 = _iterator17[_i17++];
          } else {
            _i17 = _iterator17.next();
            if (_i17.done) break;
            _ref18 = _i17.value;
          }

          var _rule2 = _ref18;

          if (this.classSequenceMatches(-_rule2.backtrack.length, _rule2.backtrack, table.backtrackClassDef) && this.classSequenceMatches(1, _rule2.input, table.inputClassDef) && this.classSequenceMatches(1 + _rule2.input.length, _rule2.lookahead, table.lookaheadClassDef)) {
            return this.applyLookupList(_rule2.lookupRecords);
          }
        }

        break;

      case 3:
        if (this.coverageSequenceMatches(-table.backtrackGlyphCount, table.backtrackCoverage) && this.coverageSequenceMatches(0, table.inputCoverage) && this.coverageSequenceMatches(table.inputGlyphCount, table.lookaheadCoverage)) {
          return this.applyLookupList(table.lookupRecords);
        }

        break;
    }

    return false;
  };

  return OTProcessor;
}();

var GlyphInfo = function () {
  function GlyphInfo(font, id) {
    var codePoints = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
    var features = arguments[3];

    _classCallCheck(this, GlyphInfo);

    this._font = font;
    this.codePoints = codePoints;
    this.id = id;

    this.features = {};
    if (Array.isArray(features)) {
      for (var i = 0; i < features.length; i++) {
        var feature = features[i];
        this.features[feature] = true;
      }
    } else if ((typeof features === 'undefined' ? 'undefined' : _typeof(features)) === 'object') {
      _Object$assign(this.features, features);
    }

    this.ligatureID = null;
    this.ligatureComponent = null;
    this.isLigated = false;
    this.cursiveAttachment = null;
    this.markAttachment = null;
    this.shaperInfo = null;
    this.substituted = false;
    this.isMultiplied = false;
  }

  GlyphInfo.prototype.copy = function copy() {
    return new GlyphInfo(this._font, this.id, this.codePoints, this.features);
  };

  _createClass(GlyphInfo, [{
    key: 'id',
    get: function get() {
      return this._id;
    },
    set: function set(id) {
      this._id = id;
      this.substituted = true;

      var GDEF = this._font.GDEF;
      if (GDEF && GDEF.glyphClassDef) {
        // TODO: clean this up
        var classID = OTProcessor.prototype.getClassID(id, GDEF.glyphClassDef);
        this.isBase = classID === 1;
        this.isLigature = classID === 2;
        this.isMark = classID === 3;
        this.markAttachmentType = GDEF.markAttachClassDef ? OTProcessor.prototype.getClassID(id, GDEF.markAttachClassDef) : 0;
      } else {
        this.isMark = this.codePoints.length > 0 && this.codePoints.every(unicode.isMark);
        this.isBase = !this.isMark;
        this.isLigature = this.codePoints.length > 1;
        this.markAttachmentType = 0;
      }
    }
  }]);

  return GlyphInfo;
}();

var _class$5;
var _temp$1;
/**
 * This is a shaper for the Hangul script, used by the Korean language.
 * It does the following:
 *   - decompose if unsupported by the font:
 *     <LV>   -> <L,V>
 *     <LVT>  -> <L,V,T>
 *     <LV,T> -> <L,V,T>
 *
 *   - compose if supported by the font:
 *     <L,V>   -> <LV>
 *     <L,V,T> -> <LVT>
 *     <LV,T>  -> <LVT>
 *
 *   - reorder tone marks (S is any valid syllable):
 *     <S, M> -> <M, S>
 *
 *   - apply ljmo, vjmo, and tjmo OpenType features to decomposed Jamo sequences.
 *
 * This logic is based on the following documents:
 *   - http://www.microsoft.com/typography/OpenTypeDev/hangul/intro.htm
 *   - http://ktug.org/~nomos/harfbuzz-hangul/hangulshaper.pdf
 */
var HangulShaper = (_temp$1 = _class$5 = function (_DefaultShaper) {
  _inherits(HangulShaper, _DefaultShaper);

  function HangulShaper() {
    _classCallCheck(this, HangulShaper);

    return _possibleConstructorReturn(this, _DefaultShaper.apply(this, arguments));
  }

  HangulShaper.planFeatures = function planFeatures(plan) {
    plan.add(['ljmo', 'vjmo', 'tjmo'], false);
  };

  HangulShaper.assignFeatures = function assignFeatures(plan, glyphs) {
    var state = 0;
    var i = 0;
    while (i < glyphs.length) {
      var action = void 0;
      var glyph = glyphs[i];
      var code = glyph.codePoints[0];
      var type = getType(code);

      var _STATE_TABLE$state$ty = STATE_TABLE$1[state][type];
      action = _STATE_TABLE$state$ty[0];
      state = _STATE_TABLE$state$ty[1];


      switch (action) {
        case DECOMPOSE:
          // Decompose the composed syllable if it is not supported by the font.
          if (!plan.font.hasGlyphForCodePoint(code)) {
            i = decompose(glyphs, i, plan.font);
          }
          break;

        case COMPOSE:
          // Found a decomposed syllable. Try to compose if supported by the font.
          i = compose(glyphs, i, plan.font);
          break;

        case TONE_MARK:
          // Got a valid syllable, followed by a tone mark. Move the tone mark to the beginning of the syllable.
          reorderToneMark(glyphs, i, plan.font);
          break;

        case INVALID:
          // Tone mark has no valid syllable to attach to, so insert a dotted circle
          i = insertDottedCircle(glyphs, i, plan.font);
          break;
      }

      i++;
    }
  };

  return HangulShaper;
}(DefaultShaper), _class$5.zeroMarkWidths = 'NONE', _temp$1);
var HANGUL_BASE = 0xac00;
var HANGUL_END = 0xd7a4;
var HANGUL_COUNT = HANGUL_END - HANGUL_BASE + 1;
var L_BASE = 0x1100; // lead
var V_BASE = 0x1161; // vowel
var T_BASE = 0x11a7; // trail
var L_COUNT = 19;
var V_COUNT = 21;
var T_COUNT = 28;
var L_END = L_BASE + L_COUNT - 1;
var V_END = V_BASE + V_COUNT - 1;
var T_END = T_BASE + T_COUNT - 1;
var DOTTED_CIRCLE = 0x25cc;

var isL = function isL(code) {
  return 0x1100 <= code && code <= 0x115f || 0xa960 <= code && code <= 0xa97c;
};
var isV = function isV(code) {
  return 0x1160 <= code && code <= 0x11a7 || 0xd7b0 <= code && code <= 0xd7c6;
};
var isT = function isT(code) {
  return 0x11a8 <= code && code <= 0x11ff || 0xd7cb <= code && code <= 0xd7fb;
};
var isTone = function isTone(code) {
  return 0x302e <= code && code <= 0x302f;
};
var isLVT = function isLVT(code) {
  return HANGUL_BASE <= code && code <= HANGUL_END;
};
var isLV = function isLV(code) {
  return code - HANGUL_BASE < HANGUL_COUNT && (code - HANGUL_BASE) % T_COUNT === 0;
};
var isCombiningL = function isCombiningL(code) {
  return L_BASE <= code && code <= L_END;
};
var isCombiningV = function isCombiningV(code) {
  return V_BASE <= code && code <= V_END;
};
var isCombiningT = function isCombiningT(code) {
  return T_BASE + 1 && 1 <= code && code <= T_END;
};

// Character categories
var X = 0; // Other character
var L = 1; // Leading consonant
var V = 2; // Medial vowel
var T = 3; // Trailing consonant
var LV = 4; // Composed <LV> syllable
var LVT = 5; // Composed <LVT> syllable
var M = 6; // Tone mark

// This function classifies a character using the above categories.
function getType(code) {
  if (isL(code)) {
    return L;
  }
  if (isV(code)) {
    return V;
  }
  if (isT(code)) {
    return T;
  }
  if (isLV(code)) {
    return LV;
  }
  if (isLVT(code)) {
    return LVT;
  }
  if (isTone(code)) {
    return M;
  }
  return X;
}

// State machine actions
var NO_ACTION = 0;
var DECOMPOSE = 1;
var COMPOSE = 2;
var TONE_MARK = 4;
var INVALID = 5;

// Build a state machine that accepts valid syllables, and applies actions along the way.
// The logic this is implementing is documented at the top of the file.
var STATE_TABLE$1 = [
//       X                 L                 V                T                  LV                LVT               M
// State 0: start state
[[NO_ACTION, 0], [NO_ACTION, 1], [NO_ACTION, 0], [NO_ACTION, 0], [DECOMPOSE, 2], [DECOMPOSE, 3], [INVALID, 0]],

// State 1: <L>
[[NO_ACTION, 0], [NO_ACTION, 1], [COMPOSE, 2], [NO_ACTION, 0], [DECOMPOSE, 2], [DECOMPOSE, 3], [INVALID, 0]],

// State 2: <L,V> or <LV>
[[NO_ACTION, 0], [NO_ACTION, 1], [NO_ACTION, 0], [COMPOSE, 3], [DECOMPOSE, 2], [DECOMPOSE, 3], [TONE_MARK, 0]],

// State 3: <L,V,T> or <LVT>
[[NO_ACTION, 0], [NO_ACTION, 1], [NO_ACTION, 0], [NO_ACTION, 0], [DECOMPOSE, 2], [DECOMPOSE, 3], [TONE_MARK, 0]]];

function getGlyph(font, code, features) {
  return new GlyphInfo(font, font.glyphForCodePoint(code).id, [code], features);
}

function decompose(glyphs, i, font) {
  var glyph = glyphs[i];
  var code = glyph.codePoints[0];

  var s = code - HANGUL_BASE;
  var t = T_BASE + s % T_COUNT;
  s = s / T_COUNT | 0;
  var l = L_BASE + s / V_COUNT | 0;
  var v = V_BASE + s % V_COUNT;

  // Don't decompose if all of the components are not available
  if (!font.hasGlyphForCodePoint(l) || !font.hasGlyphForCodePoint(v) || t !== T_BASE && !font.hasGlyphForCodePoint(t)) {
    return i;
  }

  // Replace the current glyph with decomposed L, V, and T glyphs,
  // and apply the proper OpenType features to each component.
  var ljmo = getGlyph(font, l, glyph.features);
  ljmo.features.ljmo = true;

  var vjmo = getGlyph(font, v, glyph.features);
  vjmo.features.vjmo = true;

  var insert = [ljmo, vjmo];

  if (t > T_BASE) {
    var tjmo = getGlyph(font, t, glyph.features);
    tjmo.features.tjmo = true;
    insert.push(tjmo);
  }

  glyphs.splice.apply(glyphs, [i, 1].concat(insert));
  return i + insert.length - 1;
}

function compose(glyphs, i, font) {
  var glyph = glyphs[i];
  var code = glyphs[i].codePoints[0];
  var type = getType(code);

  var prev = glyphs[i - 1].codePoints[0];
  var prevType = getType(prev);

  // Figure out what type of syllable we're dealing with
  var lv = void 0,
      ljmo = void 0,
      vjmo = void 0,
      tjmo = void 0;
  if (prevType === LV && type === T) {
    // <LV,T>
    lv = prev;
    tjmo = glyph;
  } else {
    if (type === V) {
      // <L,V>
      ljmo = glyphs[i - 1];
      vjmo = glyph;
    } else {
      // <L,V,T>
      ljmo = glyphs[i - 2];
      vjmo = glyphs[i - 1];
      tjmo = glyph;
    }

    var l = ljmo.codePoints[0];
    var v = vjmo.codePoints[0];

    // Make sure L and V are combining characters
    if (isCombiningL(l) && isCombiningV(v)) {
      lv = HANGUL_BASE + ((l - L_BASE) * V_COUNT + (v - V_BASE)) * T_COUNT;
    }
  }

  var t = tjmo && tjmo.codePoints[0] || T_BASE;
  if (lv != null && (t === T_BASE || isCombiningT(t))) {
    var s = lv + (t - T_BASE);

    // Replace with a composed glyph if supported by the font,
    // otherwise apply the proper OpenType features to each component.
    if (font.hasGlyphForCodePoint(s)) {
      var del = prevType === V ? 3 : 2;
      glyphs.splice(i - del + 1, del, getGlyph(font, s, glyph.features));
      return i - del + 1;
    }
  }

  // Didn't compose (either a non-combining component or unsupported by font).
  if (ljmo) {
    ljmo.features.ljmo = true;
  }
  if (vjmo) {
    vjmo.features.vjmo = true;
  }
  if (tjmo) {
    tjmo.features.tjmo = true;
  }

  if (prevType === LV) {
    // Sequence was originally <L,V>, which got combined earlier.
    // Either the T was non-combining, or the LVT glyph wasn't supported.
    // Decompose the glyph again and apply OT features.
    decompose(glyphs, i - 1, font);
    return i + 1;
  }

  return i;
}

function getLength(code) {
  switch (getType(code)) {
    case LV:
    case LVT:
      return 1;
    case V:
      return 2;
    case T:
      return 3;
  }
}

function reorderToneMark(glyphs, i, font) {
  var glyph = glyphs[i];
  var code = glyphs[i].codePoints[0];

  // Move tone mark to the beginning of the previous syllable, unless it is zero width
  if (font.glyphForCodePoint(code).advanceWidth === 0) {
    return;
  }

  var prev = glyphs[i - 1].codePoints[0];
  var len = getLength(prev);

  glyphs.splice(i, 1);
  return glyphs.splice(i - len, 0, glyph);
}

function insertDottedCircle(glyphs, i, font) {
  var glyph = glyphs[i];
  var code = glyphs[i].codePoints[0];

  if (font.hasGlyphForCodePoint(DOTTED_CIRCLE)) {
    var dottedCircle = getGlyph(font, DOTTED_CIRCLE, glyph.features);

    // If the tone mark is zero width, insert the dotted circle before, otherwise after
    var idx = font.glyphForCodePoint(code).advanceWidth === 0 ? i : i + 1;
    glyphs.splice(idx, 0, dottedCircle);
    i++;
  }

  return i;
}

var stateTable = [[0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 2, 3, 4, 5, 6, 7, 8, 9, 0, 10, 11, 11, 12, 13, 14, 15, 16, 17], [0, 0, 0, 18, 19, 20, 21, 22, 23, 0, 24, 0, 0, 25, 26, 0, 0, 27, 0], [0, 0, 0, 28, 29, 30, 31, 32, 33, 0, 34, 0, 0, 35, 36, 0, 0, 37, 0], [0, 0, 0, 38, 5, 7, 7, 8, 9, 0, 10, 0, 0, 0, 13, 0, 0, 16, 0], [0, 39, 0, 0, 0, 40, 41, 0, 9, 0, 10, 0, 0, 0, 42, 0, 39, 0, 0], [0, 0, 0, 0, 43, 44, 44, 8, 9, 0, 0, 0, 0, 12, 43, 0, 0, 0, 0], [0, 0, 0, 0, 43, 44, 44, 8, 9, 0, 0, 0, 0, 0, 43, 0, 0, 0, 0], [0, 0, 0, 45, 46, 47, 48, 49, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 50, 0, 0, 51, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 52, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 53, 54, 55, 56, 57, 58, 0, 59, 0, 0, 60, 61, 0, 0, 62, 0], [0, 0, 0, 4, 5, 7, 7, 8, 9, 0, 10, 0, 0, 0, 13, 0, 0, 16, 0], [0, 63, 64, 0, 0, 40, 41, 0, 9, 0, 10, 0, 0, 0, 42, 0, 63, 0, 0], [0, 2, 3, 4, 5, 6, 7, 8, 9, 0, 10, 11, 11, 12, 13, 0, 2, 16, 0], [0, 0, 0, 18, 65, 20, 21, 22, 23, 0, 24, 0, 0, 25, 26, 0, 0, 27, 0], [0, 0, 0, 0, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 0, 0], [0, 0, 0, 69, 0, 70, 70, 0, 71, 0, 72, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 73, 19, 74, 74, 22, 23, 0, 24, 0, 0, 0, 26, 0, 0, 27, 0], [0, 75, 0, 0, 0, 76, 77, 0, 23, 0, 24, 0, 0, 0, 78, 0, 75, 0, 0], [0, 0, 0, 0, 79, 80, 80, 22, 23, 0, 0, 0, 0, 25, 79, 0, 0, 0, 0], [0, 0, 0, 18, 19, 20, 74, 22, 23, 0, 24, 0, 0, 25, 26, 0, 0, 27, 0], [0, 0, 0, 81, 82, 83, 84, 85, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 86, 0, 0, 87, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 88, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 18, 19, 74, 74, 22, 23, 0, 24, 0, 0, 0, 26, 0, 0, 27, 0], [0, 89, 90, 0, 0, 76, 77, 0, 23, 0, 24, 0, 0, 0, 78, 0, 89, 0, 0], [0, 0, 0, 0, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 0, 0], [0, 0, 0, 94, 29, 95, 31, 32, 33, 0, 34, 0, 0, 0, 36, 0, 0, 37, 0], [0, 96, 0, 0, 0, 97, 98, 0, 33, 0, 34, 0, 0, 0, 99, 0, 96, 0, 0], [0, 0, 0, 0, 100, 101, 101, 32, 33, 0, 0, 0, 0, 35, 100, 0, 0, 0, 0], [0, 0, 0, 0, 100, 101, 101, 32, 33, 0, 0, 0, 0, 0, 100, 0, 0, 0, 0], [0, 0, 0, 102, 103, 104, 105, 106, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 107, 0, 0, 108, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 109, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 28, 29, 95, 31, 32, 33, 0, 34, 0, 0, 0, 36, 0, 0, 37, 0], [0, 110, 111, 0, 0, 97, 98, 0, 33, 0, 34, 0, 0, 0, 99, 0, 110, 0, 0], [0, 0, 0, 0, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 0, 0], [0, 0, 0, 0, 5, 7, 7, 8, 9, 0, 10, 0, 0, 0, 13, 0, 0, 16, 0], [0, 0, 0, 115, 116, 117, 118, 8, 9, 0, 10, 0, 0, 119, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 39, 0, 122, 0, 123, 123, 8, 9, 0, 10, 0, 0, 0, 42, 0, 39, 0, 0], [0, 124, 64, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 124, 0, 0], [0, 39, 0, 0, 0, 121, 125, 0, 9, 0, 10, 0, 0, 0, 42, 0, 39, 0, 0], [0, 0, 0, 0, 0, 126, 126, 8, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 46, 47, 48, 49, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 47, 47, 49, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 127, 127, 49, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 128, 127, 127, 49, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 129, 130, 131, 132, 133, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 50, 0, 0, 0, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 134, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 135, 54, 56, 56, 57, 58, 0, 59, 0, 0, 0, 61, 0, 0, 62, 0], [0, 136, 0, 0, 0, 137, 138, 0, 58, 0, 59, 0, 0, 0, 139, 0, 136, 0, 0], [0, 0, 0, 0, 140, 141, 141, 57, 58, 0, 0, 0, 0, 60, 140, 0, 0, 0, 0], [0, 0, 0, 0, 140, 141, 141, 57, 58, 0, 0, 0, 0, 0, 140, 0, 0, 0, 0], [0, 0, 0, 142, 143, 144, 145, 146, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 147, 0, 0, 148, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 149, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 53, 54, 56, 56, 57, 58, 0, 59, 0, 0, 0, 61, 0, 0, 62, 0], [0, 150, 151, 0, 0, 137, 138, 0, 58, 0, 59, 0, 0, 0, 139, 0, 150, 0, 0], [0, 0, 0, 0, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 0, 0], [0, 0, 0, 155, 116, 156, 157, 8, 9, 0, 10, 0, 0, 158, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 75, 3, 4, 5, 159, 160, 8, 161, 0, 162, 0, 11, 12, 163, 0, 75, 16, 0], [0, 0, 0, 0, 0, 40, 164, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 165, 44, 44, 8, 9, 0, 0, 0, 0, 0, 165, 0, 0, 0, 0], [0, 124, 64, 0, 0, 40, 164, 0, 9, 0, 10, 0, 0, 0, 42, 0, 124, 0, 0], [0, 0, 0, 0, 0, 70, 70, 0, 71, 0, 72, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 71, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 166, 0, 0, 167, 0, 72, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 168, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 19, 74, 74, 22, 23, 0, 24, 0, 0, 0, 26, 0, 0, 27, 0], [0, 0, 0, 0, 79, 80, 80, 22, 23, 0, 0, 0, 0, 0, 79, 0, 0, 0, 0], [0, 0, 0, 169, 170, 171, 172, 22, 23, 0, 24, 0, 0, 173, 174, 0, 0, 27, 0], [0, 0, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 75, 0, 176, 0, 177, 177, 22, 23, 0, 24, 0, 0, 0, 78, 0, 75, 0, 0], [0, 178, 90, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 178, 0, 0], [0, 75, 0, 0, 0, 175, 179, 0, 23, 0, 24, 0, 0, 0, 78, 0, 75, 0, 0], [0, 0, 0, 0, 0, 180, 180, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 82, 83, 84, 85, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 83, 83, 85, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 181, 181, 85, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 182, 181, 181, 85, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 183, 184, 185, 186, 187, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 86, 0, 0, 0, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 188, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 189, 170, 190, 191, 22, 23, 0, 24, 0, 0, 192, 174, 0, 0, 27, 0], [0, 0, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 76, 193, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 194, 80, 80, 22, 23, 0, 0, 0, 0, 0, 194, 0, 0, 0, 0], [0, 178, 90, 0, 0, 76, 193, 0, 23, 0, 24, 0, 0, 0, 78, 0, 178, 0, 0], [0, 0, 0, 0, 29, 95, 31, 32, 33, 0, 34, 0, 0, 0, 36, 0, 0, 37, 0], [0, 0, 0, 0, 100, 101, 101, 32, 33, 0, 0, 0, 0, 0, 100, 0, 0, 0, 0], [0, 0, 0, 195, 196, 197, 198, 32, 33, 0, 34, 0, 0, 199, 200, 0, 0, 37, 0], [0, 0, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 96, 0, 202, 0, 203, 203, 32, 33, 0, 34, 0, 0, 0, 99, 0, 96, 0, 0], [0, 204, 111, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 204, 0, 0], [0, 96, 0, 0, 0, 201, 205, 0, 33, 0, 34, 0, 0, 0, 99, 0, 96, 0, 0], [0, 0, 0, 0, 0, 206, 206, 32, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 103, 104, 105, 106, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 104, 104, 106, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 207, 207, 106, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 208, 207, 207, 106, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 209, 210, 211, 212, 213, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 107, 0, 0, 0, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 214, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 215, 196, 216, 217, 32, 33, 0, 34, 0, 0, 218, 200, 0, 0, 37, 0], [0, 0, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 97, 219, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 220, 101, 101, 32, 33, 0, 0, 0, 0, 0, 220, 0, 0, 0, 0], [0, 204, 111, 0, 0, 97, 219, 0, 33, 0, 34, 0, 0, 0, 99, 0, 204, 0, 0], [0, 0, 0, 221, 116, 222, 222, 8, 9, 0, 10, 0, 0, 0, 120, 0, 0, 16, 0], [0, 223, 0, 0, 0, 40, 224, 0, 9, 0, 10, 0, 0, 0, 42, 0, 223, 0, 0], [0, 0, 0, 0, 225, 44, 44, 8, 9, 0, 0, 0, 0, 119, 225, 0, 0, 0, 0], [0, 0, 0, 115, 116, 117, 222, 8, 9, 0, 10, 0, 0, 119, 120, 0, 0, 16, 0], [0, 0, 0, 115, 116, 222, 222, 8, 9, 0, 10, 0, 0, 0, 120, 0, 0, 16, 0], [0, 226, 64, 0, 0, 40, 224, 0, 9, 0, 10, 0, 0, 0, 42, 0, 226, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 39, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 39, 0, 0], [0, 0, 0, 0, 0, 44, 44, 8, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 227, 0, 228, 229, 0, 9, 0, 10, 0, 0, 230, 0, 0, 0, 0, 0], [0, 39, 0, 122, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 39, 0, 0], [0, 0, 0, 0, 0, 0, 0, 8, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 231, 231, 49, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 232, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 130, 131, 132, 133, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 131, 131, 133, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 233, 233, 133, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 234, 233, 233, 133, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 235, 236, 237, 238, 239, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 54, 56, 56, 57, 58, 0, 59, 0, 0, 0, 61, 0, 0, 62, 0], [0, 0, 0, 240, 241, 242, 243, 57, 58, 0, 59, 0, 0, 244, 245, 0, 0, 62, 0], [0, 0, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 136, 0, 247, 0, 248, 248, 57, 58, 0, 59, 0, 0, 0, 139, 0, 136, 0, 0], [0, 249, 151, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 249, 0, 0], [0, 136, 0, 0, 0, 246, 250, 0, 58, 0, 59, 0, 0, 0, 139, 0, 136, 0, 0], [0, 0, 0, 0, 0, 251, 251, 57, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 143, 144, 145, 146, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 144, 144, 146, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 252, 252, 146, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 253, 252, 252, 146, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 254, 255, 256, 257, 258, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 147, 0, 0, 0, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 259, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 260, 241, 261, 262, 57, 58, 0, 59, 0, 0, 263, 245, 0, 0, 62, 0], [0, 0, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 137, 264, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 265, 141, 141, 57, 58, 0, 0, 0, 0, 0, 265, 0, 0, 0, 0], [0, 249, 151, 0, 0, 137, 264, 0, 58, 0, 59, 0, 0, 0, 139, 0, 249, 0, 0], [0, 0, 0, 221, 116, 222, 222, 8, 9, 0, 10, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 225, 44, 44, 8, 9, 0, 0, 0, 0, 158, 225, 0, 0, 0, 0], [0, 0, 0, 155, 116, 156, 222, 8, 9, 0, 10, 0, 0, 158, 120, 0, 0, 16, 0], [0, 0, 0, 155, 116, 222, 222, 8, 9, 0, 10, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 43, 266, 266, 8, 161, 0, 24, 0, 0, 12, 267, 0, 0, 0, 0], [0, 75, 0, 176, 43, 268, 268, 269, 161, 0, 24, 0, 0, 0, 267, 0, 75, 0, 0], [0, 0, 0, 0, 0, 270, 0, 0, 271, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 272, 0, 0, 0, 0, 0, 0, 0, 0], [0, 273, 274, 0, 0, 40, 41, 0, 9, 0, 10, 0, 0, 0, 42, 0, 273, 0, 0], [0, 0, 0, 40, 0, 123, 123, 8, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 121, 275, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 72, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 166, 0, 0, 0, 0, 72, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 276, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 277, 170, 278, 278, 22, 23, 0, 24, 0, 0, 0, 174, 0, 0, 27, 0], [0, 279, 0, 0, 0, 76, 280, 0, 23, 0, 24, 0, 0, 0, 78, 0, 279, 0, 0], [0, 0, 0, 0, 281, 80, 80, 22, 23, 0, 0, 0, 0, 173, 281, 0, 0, 0, 0], [0, 0, 0, 169, 170, 171, 278, 22, 23, 0, 24, 0, 0, 173, 174, 0, 0, 27, 0], [0, 0, 0, 169, 170, 278, 278, 22, 23, 0, 24, 0, 0, 0, 174, 0, 0, 27, 0], [0, 282, 90, 0, 0, 76, 280, 0, 23, 0, 24, 0, 0, 0, 78, 0, 282, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 75, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 75, 0, 0], [0, 0, 0, 0, 0, 80, 80, 22, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 283, 0, 284, 285, 0, 23, 0, 24, 0, 0, 286, 0, 0, 0, 0, 0], [0, 75, 0, 176, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 75, 0, 0], [0, 0, 0, 0, 0, 0, 0, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 287, 287, 85, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 288, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 184, 185, 186, 187, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 185, 185, 187, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 289, 289, 187, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 290, 289, 289, 187, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 291, 292, 293, 294, 295, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 277, 170, 278, 278, 22, 23, 0, 24, 0, 0, 0, 174, 0, 0, 27, 0], [0, 0, 0, 0, 281, 80, 80, 22, 23, 0, 0, 0, 0, 192, 281, 0, 0, 0, 0], [0, 0, 0, 189, 170, 190, 278, 22, 23, 0, 24, 0, 0, 192, 174, 0, 0, 27, 0], [0, 0, 0, 189, 170, 278, 278, 22, 23, 0, 24, 0, 0, 0, 174, 0, 0, 27, 0], [0, 0, 0, 76, 0, 177, 177, 22, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 175, 296, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 297, 196, 298, 298, 32, 33, 0, 34, 0, 0, 0, 200, 0, 0, 37, 0], [0, 299, 0, 0, 0, 97, 300, 0, 33, 0, 34, 0, 0, 0, 99, 0, 299, 0, 0], [0, 0, 0, 0, 301, 101, 101, 32, 33, 0, 0, 0, 0, 199, 301, 0, 0, 0, 0], [0, 0, 0, 195, 196, 197, 298, 32, 33, 0, 34, 0, 0, 199, 200, 0, 0, 37, 0], [0, 0, 0, 195, 196, 298, 298, 32, 33, 0, 34, 0, 0, 0, 200, 0, 0, 37, 0], [0, 302, 111, 0, 0, 97, 300, 0, 33, 0, 34, 0, 0, 0, 99, 0, 302, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 96, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 96, 0, 0], [0, 0, 0, 0, 0, 101, 101, 32, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 303, 0, 304, 305, 0, 33, 0, 34, 0, 0, 306, 0, 0, 0, 0, 0], [0, 96, 0, 202, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 96, 0, 0], [0, 0, 0, 0, 0, 0, 0, 32, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 307, 307, 106, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 308, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 210, 211, 212, 213, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 211, 211, 213, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 309, 309, 213, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 310, 309, 309, 213, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 311, 312, 313, 314, 315, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 297, 196, 298, 298, 32, 33, 0, 34, 0, 0, 0, 200, 0, 0, 37, 0], [0, 0, 0, 0, 301, 101, 101, 32, 33, 0, 0, 0, 0, 218, 301, 0, 0, 0, 0], [0, 0, 0, 215, 196, 216, 298, 32, 33, 0, 34, 0, 0, 218, 200, 0, 0, 37, 0], [0, 0, 0, 215, 196, 298, 298, 32, 33, 0, 34, 0, 0, 0, 200, 0, 0, 37, 0], [0, 0, 0, 97, 0, 203, 203, 32, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 201, 316, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 116, 222, 222, 8, 9, 0, 10, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 225, 44, 44, 8, 9, 0, 0, 0, 0, 0, 225, 0, 0, 0, 0], [0, 0, 0, 317, 318, 319, 320, 8, 9, 0, 10, 0, 0, 321, 322, 0, 0, 16, 0], [0, 223, 0, 323, 0, 123, 123, 8, 9, 0, 10, 0, 0, 0, 42, 0, 223, 0, 0], [0, 223, 0, 0, 0, 121, 324, 0, 9, 0, 10, 0, 0, 0, 42, 0, 223, 0, 0], [0, 0, 0, 325, 318, 326, 327, 8, 9, 0, 10, 0, 0, 328, 322, 0, 0, 16, 0], [0, 0, 0, 64, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 9, 0, 0, 0, 0, 230, 0, 0, 0, 0, 0], [0, 0, 0, 227, 0, 228, 121, 0, 9, 0, 10, 0, 0, 230, 0, 0, 0, 0, 0], [0, 0, 0, 227, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 49, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 46, 0, 0], [0, 0, 0, 0, 0, 329, 329, 133, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 330, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 236, 237, 238, 239, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 237, 237, 239, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 331, 331, 239, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 332, 331, 331, 239, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 333, 40, 121, 334, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 335, 241, 336, 336, 57, 58, 0, 59, 0, 0, 0, 245, 0, 0, 62, 0], [0, 337, 0, 0, 0, 137, 338, 0, 58, 0, 59, 0, 0, 0, 139, 0, 337, 0, 0], [0, 0, 0, 0, 339, 141, 141, 57, 58, 0, 0, 0, 0, 244, 339, 0, 0, 0, 0], [0, 0, 0, 240, 241, 242, 336, 57, 58, 0, 59, 0, 0, 244, 245, 0, 0, 62, 0], [0, 0, 0, 240, 241, 336, 336, 57, 58, 0, 59, 0, 0, 0, 245, 0, 0, 62, 0], [0, 340, 151, 0, 0, 137, 338, 0, 58, 0, 59, 0, 0, 0, 139, 0, 340, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 136, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 136, 0, 0], [0, 0, 0, 0, 0, 141, 141, 57, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 341, 0, 342, 343, 0, 58, 0, 59, 0, 0, 344, 0, 0, 0, 0, 0], [0, 136, 0, 247, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 136, 0, 0], [0, 0, 0, 0, 0, 0, 0, 57, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 345, 345, 146, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 346, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 255, 256, 257, 258, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 256, 256, 258, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 347, 347, 258, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 348, 347, 347, 258, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 349, 350, 351, 352, 353, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 335, 241, 336, 336, 57, 58, 0, 59, 0, 0, 0, 245, 0, 0, 62, 0], [0, 0, 0, 0, 339, 141, 141, 57, 58, 0, 0, 0, 0, 263, 339, 0, 0, 0, 0], [0, 0, 0, 260, 241, 261, 336, 57, 58, 0, 59, 0, 0, 263, 245, 0, 0, 62, 0], [0, 0, 0, 260, 241, 336, 336, 57, 58, 0, 59, 0, 0, 0, 245, 0, 0, 62, 0], [0, 0, 0, 137, 0, 248, 248, 57, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 246, 354, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 126, 126, 8, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 355, 90, 0, 0, 121, 125, 0, 9, 0, 10, 0, 0, 0, 42, 0, 355, 0, 0], [0, 0, 0, 0, 0, 356, 356, 269, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 357, 358, 359, 360, 361, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 270, 0, 0, 0, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 363, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 364, 116, 365, 366, 8, 161, 0, 162, 0, 0, 367, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 368, 368, 0, 161, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 40, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 170, 278, 278, 22, 23, 0, 24, 0, 0, 0, 174, 0, 0, 27, 0], [0, 0, 0, 0, 281, 80, 80, 22, 23, 0, 0, 0, 0, 0, 281, 0, 0, 0, 0], [0, 0, 0, 369, 370, 371, 372, 22, 23, 0, 24, 0, 0, 373, 374, 0, 0, 27, 0], [0, 279, 0, 375, 0, 177, 177, 22, 23, 0, 24, 0, 0, 0, 78, 0, 279, 0, 0], [0, 279, 0, 0, 0, 175, 376, 0, 23, 0, 24, 0, 0, 0, 78, 0, 279, 0, 0], [0, 0, 0, 377, 370, 378, 379, 22, 23, 0, 24, 0, 0, 380, 374, 0, 0, 27, 0], [0, 0, 0, 90, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 23, 0, 0, 0, 0, 286, 0, 0, 0, 0, 0], [0, 0, 0, 283, 0, 284, 175, 0, 23, 0, 24, 0, 0, 286, 0, 0, 0, 0, 0], [0, 0, 0, 283, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 85, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 82, 0, 0], [0, 0, 0, 0, 0, 381, 381, 187, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 382, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 292, 293, 294, 295, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 293, 293, 295, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 383, 383, 295, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 384, 383, 383, 295, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 385, 76, 175, 386, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 76, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 196, 298, 298, 32, 33, 0, 34, 0, 0, 0, 200, 0, 0, 37, 0], [0, 0, 0, 0, 301, 101, 101, 32, 33, 0, 0, 0, 0, 0, 301, 0, 0, 0, 0], [0, 0, 0, 387, 388, 389, 390, 32, 33, 0, 34, 0, 0, 391, 392, 0, 0, 37, 0], [0, 299, 0, 393, 0, 203, 203, 32, 33, 0, 34, 0, 0, 0, 99, 0, 299, 0, 0], [0, 299, 0, 0, 0, 201, 394, 0, 33, 0, 34, 0, 0, 0, 99, 0, 299, 0, 0], [0, 0, 0, 395, 388, 396, 397, 32, 33, 0, 34, 0, 0, 398, 392, 0, 0, 37, 0], [0, 0, 0, 111, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 33, 0, 0, 0, 0, 306, 0, 0, 0, 0, 0], [0, 0, 0, 303, 0, 304, 201, 0, 33, 0, 34, 0, 0, 306, 0, 0, 0, 0, 0], [0, 0, 0, 303, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 106, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 103, 0, 0], [0, 0, 0, 0, 0, 399, 399, 213, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 400, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 312, 313, 314, 315, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 313, 313, 315, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 401, 401, 315, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 402, 401, 401, 315, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 403, 97, 201, 404, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 97, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 405, 318, 406, 406, 8, 9, 0, 10, 0, 0, 0, 322, 0, 0, 16, 0], [0, 407, 0, 0, 0, 40, 408, 0, 9, 0, 10, 0, 0, 0, 42, 0, 407, 0, 0], [0, 0, 0, 0, 409, 44, 44, 8, 9, 0, 0, 0, 0, 321, 409, 0, 0, 0, 0], [0, 0, 0, 317, 318, 319, 406, 8, 9, 0, 10, 0, 0, 321, 322, 0, 0, 16, 0], [0, 0, 0, 317, 318, 406, 406, 8, 9, 0, 10, 0, 0, 0, 322, 0, 0, 16, 0], [0, 410, 64, 0, 0, 40, 408, 0, 9, 0, 10, 0, 0, 0, 42, 0, 410, 0, 0], [0, 223, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 223, 0, 0], [0, 223, 0, 323, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 223, 0, 0], [0, 0, 0, 405, 318, 406, 406, 8, 9, 0, 10, 0, 0, 0, 322, 0, 0, 16, 0], [0, 0, 0, 0, 409, 44, 44, 8, 9, 0, 0, 0, 0, 328, 409, 0, 0, 0, 0], [0, 0, 0, 325, 318, 326, 406, 8, 9, 0, 10, 0, 0, 328, 322, 0, 0, 16, 0], [0, 0, 0, 325, 318, 406, 406, 8, 9, 0, 10, 0, 0, 0, 322, 0, 0, 16, 0], [0, 0, 0, 0, 0, 0, 0, 133, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 130, 0, 0], [0, 0, 0, 0, 0, 411, 411, 239, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 412, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 40, 121, 334, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 413, 0, 0, 0, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 241, 336, 336, 57, 58, 0, 59, 0, 0, 0, 245, 0, 0, 62, 0], [0, 0, 0, 0, 339, 141, 141, 57, 58, 0, 0, 0, 0, 0, 339, 0, 0, 0, 0], [0, 0, 0, 414, 415, 416, 417, 57, 58, 0, 59, 0, 0, 418, 419, 0, 0, 62, 0], [0, 337, 0, 420, 0, 248, 248, 57, 58, 0, 59, 0, 0, 0, 139, 0, 337, 0, 0], [0, 337, 0, 0, 0, 246, 421, 0, 58, 0, 59, 0, 0, 0, 139, 0, 337, 0, 0], [0, 0, 0, 422, 415, 423, 424, 57, 58, 0, 59, 0, 0, 425, 419, 0, 0, 62, 0], [0, 0, 0, 151, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 58, 0, 0, 0, 0, 344, 0, 0, 0, 0, 0], [0, 0, 0, 341, 0, 342, 246, 0, 58, 0, 59, 0, 0, 344, 0, 0, 0, 0, 0], [0, 0, 0, 341, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 146, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 143, 0, 0], [0, 0, 0, 0, 0, 426, 426, 258, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 427, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 350, 351, 352, 353, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 351, 351, 353, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 428, 428, 353, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 429, 428, 428, 353, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 430, 137, 246, 431, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 137, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 432, 116, 433, 434, 8, 161, 0, 162, 0, 0, 435, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 180, 180, 269, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 358, 359, 360, 361, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 359, 359, 361, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 436, 436, 361, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 437, 436, 436, 361, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 438, 439, 440, 441, 442, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 443, 274, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 443, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 444, 116, 445, 445, 8, 161, 0, 162, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 225, 44, 44, 8, 161, 0, 0, 0, 0, 367, 225, 0, 0, 0, 0], [0, 0, 0, 364, 116, 365, 445, 8, 161, 0, 162, 0, 0, 367, 120, 0, 0, 16, 0], [0, 0, 0, 364, 116, 445, 445, 8, 161, 0, 162, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 0, 0, 0, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 446, 370, 447, 447, 22, 23, 0, 24, 0, 0, 0, 374, 0, 0, 27, 0], [0, 448, 0, 0, 0, 76, 449, 0, 23, 0, 24, 0, 0, 0, 78, 0, 448, 0, 0], [0, 0, 0, 0, 450, 80, 80, 22, 23, 0, 0, 0, 0, 373, 450, 0, 0, 0, 0], [0, 0, 0, 369, 370, 371, 447, 22, 23, 0, 24, 0, 0, 373, 374, 0, 0, 27, 0], [0, 0, 0, 369, 370, 447, 447, 22, 23, 0, 24, 0, 0, 0, 374, 0, 0, 27, 0], [0, 451, 90, 0, 0, 76, 449, 0, 23, 0, 24, 0, 0, 0, 78, 0, 451, 0, 0], [0, 279, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 279, 0, 0], [0, 279, 0, 375, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 279, 0, 0], [0, 0, 0, 446, 370, 447, 447, 22, 23, 0, 24, 0, 0, 0, 374, 0, 0, 27, 0], [0, 0, 0, 0, 450, 80, 80, 22, 23, 0, 0, 0, 0, 380, 450, 0, 0, 0, 0], [0, 0, 0, 377, 370, 378, 447, 22, 23, 0, 24, 0, 0, 380, 374, 0, 0, 27, 0], [0, 0, 0, 377, 370, 447, 447, 22, 23, 0, 24, 0, 0, 0, 374, 0, 0, 27, 0], [0, 0, 0, 0, 0, 0, 0, 187, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 184, 0, 0], [0, 0, 0, 0, 0, 452, 452, 295, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 453, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 76, 175, 386, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 454, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 455, 388, 456, 456, 32, 33, 0, 34, 0, 0, 0, 392, 0, 0, 37, 0], [0, 457, 0, 0, 0, 97, 458, 0, 33, 0, 34, 0, 0, 0, 99, 0, 457, 0, 0], [0, 0, 0, 0, 459, 101, 101, 32, 33, 0, 0, 0, 0, 391, 459, 0, 0, 0, 0], [0, 0, 0, 387, 388, 389, 456, 32, 33, 0, 34, 0, 0, 391, 392, 0, 0, 37, 0], [0, 0, 0, 387, 388, 456, 456, 32, 33, 0, 34, 0, 0, 0, 392, 0, 0, 37, 0], [0, 460, 111, 0, 0, 97, 458, 0, 33, 0, 34, 0, 0, 0, 99, 0, 460, 0, 0], [0, 299, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 299, 0, 0], [0, 299, 0, 393, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 299, 0, 0], [0, 0, 0, 455, 388, 456, 456, 32, 33, 0, 34, 0, 0, 0, 392, 0, 0, 37, 0], [0, 0, 0, 0, 459, 101, 101, 32, 33, 0, 0, 0, 0, 398, 459, 0, 0, 0, 0], [0, 0, 0, 395, 388, 396, 456, 32, 33, 0, 34, 0, 0, 398, 392, 0, 0, 37, 0], [0, 0, 0, 395, 388, 456, 456, 32, 33, 0, 34, 0, 0, 0, 392, 0, 0, 37, 0], [0, 0, 0, 0, 0, 0, 0, 213, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 210, 0, 0], [0, 0, 0, 0, 0, 461, 461, 315, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 462, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 97, 201, 404, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 463, 0, 0, 0, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 318, 406, 406, 8, 9, 0, 10, 0, 0, 0, 322, 0, 0, 16, 0], [0, 0, 0, 0, 409, 44, 44, 8, 9, 0, 0, 0, 0, 0, 409, 0, 0, 0, 0], [0, 0, 0, 464, 465, 466, 467, 8, 9, 0, 10, 0, 0, 468, 469, 0, 0, 16, 0], [0, 407, 0, 470, 0, 123, 123, 8, 9, 0, 10, 0, 0, 0, 42, 0, 407, 0, 0], [0, 407, 0, 0, 0, 121, 471, 0, 9, 0, 10, 0, 0, 0, 42, 0, 407, 0, 0], [0, 0, 0, 472, 465, 473, 474, 8, 9, 0, 10, 0, 0, 475, 469, 0, 0, 16, 0], [0, 0, 0, 0, 0, 0, 0, 239, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 236, 0, 0], [0, 0, 0, 0, 0, 0, 476, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 477, 415, 478, 478, 57, 58, 0, 59, 0, 0, 0, 419, 0, 0, 62, 0], [0, 479, 0, 0, 0, 137, 480, 0, 58, 0, 59, 0, 0, 0, 139, 0, 479, 0, 0], [0, 0, 0, 0, 481, 141, 141, 57, 58, 0, 0, 0, 0, 418, 481, 0, 0, 0, 0], [0, 0, 0, 414, 415, 416, 478, 57, 58, 0, 59, 0, 0, 418, 419, 0, 0, 62, 0], [0, 0, 0, 414, 415, 478, 478, 57, 58, 0, 59, 0, 0, 0, 419, 0, 0, 62, 0], [0, 482, 151, 0, 0, 137, 480, 0, 58, 0, 59, 0, 0, 0, 139, 0, 482, 0, 0], [0, 337, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 337, 0, 0], [0, 337, 0, 420, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 337, 0, 0], [0, 0, 0, 477, 415, 478, 478, 57, 58, 0, 59, 0, 0, 0, 419, 0, 0, 62, 0], [0, 0, 0, 0, 481, 141, 141, 57, 58, 0, 0, 0, 0, 425, 481, 0, 0, 0, 0], [0, 0, 0, 422, 415, 423, 478, 57, 58, 0, 59, 0, 0, 425, 419, 0, 0, 62, 0], [0, 0, 0, 422, 415, 478, 478, 57, 58, 0, 59, 0, 0, 0, 419, 0, 0, 62, 0], [0, 0, 0, 0, 0, 0, 0, 258, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 255, 0, 0], [0, 0, 0, 0, 0, 483, 483, 353, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 484, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 137, 246, 431, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 485, 0, 0, 0, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 444, 116, 445, 445, 8, 161, 0, 162, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 225, 44, 44, 8, 161, 0, 0, 0, 0, 435, 225, 0, 0, 0, 0], [0, 0, 0, 432, 116, 433, 445, 8, 161, 0, 162, 0, 0, 435, 120, 0, 0, 16, 0], [0, 0, 0, 432, 116, 445, 445, 8, 161, 0, 162, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 486, 486, 361, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 487, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 439, 440, 441, 442, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 440, 440, 442, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 488, 488, 442, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 489, 488, 488, 442, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 490, 491, 492, 493, 494, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 495, 0, 496, 497, 0, 161, 0, 162, 0, 0, 498, 0, 0, 0, 0, 0], [0, 0, 0, 0, 116, 445, 445, 8, 161, 0, 162, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 225, 44, 44, 8, 161, 0, 0, 0, 0, 0, 225, 0, 0, 0, 0], [0, 0, 0, 0, 370, 447, 447, 22, 23, 0, 24, 0, 0, 0, 374, 0, 0, 27, 0], [0, 0, 0, 0, 450, 80, 80, 22, 23, 0, 0, 0, 0, 0, 450, 0, 0, 0, 0], [0, 0, 0, 499, 500, 501, 502, 22, 23, 0, 24, 0, 0, 503, 504, 0, 0, 27, 0], [0, 448, 0, 505, 0, 177, 177, 22, 23, 0, 24, 0, 0, 0, 78, 0, 448, 0, 0], [0, 448, 0, 0, 0, 175, 506, 0, 23, 0, 24, 0, 0, 0, 78, 0, 448, 0, 0], [0, 0, 0, 507, 500, 508, 509, 22, 23, 0, 24, 0, 0, 510, 504, 0, 0, 27, 0], [0, 0, 0, 0, 0, 0, 0, 295, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 292, 0, 0], [0, 0, 0, 0, 0, 0, 511, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 388, 456, 456, 32, 33, 0, 34, 0, 0, 0, 392, 0, 0, 37, 0], [0, 0, 0, 0, 459, 101, 101, 32, 33, 0, 0, 0, 0, 0, 459, 0, 0, 0, 0], [0, 0, 0, 512, 513, 514, 515, 32, 33, 0, 34, 0, 0, 516, 517, 0, 0, 37, 0], [0, 457, 0, 518, 0, 203, 203, 32, 33, 0, 34, 0, 0, 0, 99, 0, 457, 0, 0], [0, 457, 0, 0, 0, 201, 519, 0, 33, 0, 34, 0, 0, 0, 99, 0, 457, 0, 0], [0, 0, 0, 520, 513, 521, 522, 32, 33, 0, 34, 0, 0, 523, 517, 0, 0, 37, 0], [0, 0, 0, 0, 0, 0, 0, 315, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 312, 0, 0], [0, 0, 0, 0, 0, 0, 524, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 525, 465, 526, 526, 8, 9, 0, 10, 0, 0, 0, 469, 0, 0, 16, 0], [0, 527, 0, 0, 0, 40, 528, 0, 9, 0, 10, 0, 0, 0, 42, 0, 527, 0, 0], [0, 0, 0, 0, 529, 44, 44, 8, 9, 0, 0, 0, 0, 468, 529, 0, 0, 0, 0], [0, 0, 0, 464, 465, 466, 526, 8, 9, 0, 10, 0, 0, 468, 469, 0, 0, 16, 0], [0, 0, 0, 464, 465, 526, 526, 8, 9, 0, 10, 0, 0, 0, 469, 0, 0, 16, 0], [0, 530, 64, 0, 0, 40, 528, 0, 9, 0, 10, 0, 0, 0, 42, 0, 530, 0, 0], [0, 407, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 407, 0, 0], [0, 407, 0, 470, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 407, 0, 0], [0, 0, 0, 525, 465, 526, 526, 8, 9, 0, 10, 0, 0, 0, 469, 0, 0, 16, 0], [0, 0, 0, 0, 529, 44, 44, 8, 9, 0, 0, 0, 0, 475, 529, 0, 0, 0, 0], [0, 0, 0, 472, 465, 473, 526, 8, 9, 0, 10, 0, 0, 475, 469, 0, 0, 16, 0], [0, 0, 0, 472, 465, 526, 526, 8, 9, 0, 10, 0, 0, 0, 469, 0, 0, 16, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 40, 0, 0], [0, 0, 0, 0, 415, 478, 478, 57, 58, 0, 59, 0, 0, 0, 419, 0, 0, 62, 0], [0, 0, 0, 0, 481, 141, 141, 57, 58, 0, 0, 0, 0, 0, 481, 0, 0, 0, 0], [0, 0, 0, 531, 532, 533, 534, 57, 58, 0, 59, 0, 0, 535, 536, 0, 0, 62, 0], [0, 479, 0, 537, 0, 248, 248, 57, 58, 0, 59, 0, 0, 0, 139, 0, 479, 0, 0], [0, 479, 0, 0, 0, 246, 538, 0, 58, 0, 59, 0, 0, 0, 139, 0, 479, 0, 0], [0, 0, 0, 539, 532, 540, 541, 57, 58, 0, 59, 0, 0, 542, 536, 0, 0, 62, 0], [0, 0, 0, 0, 0, 0, 0, 353, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 350, 0, 0], [0, 0, 0, 0, 0, 0, 543, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 361, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 358, 0, 0], [0, 0, 0, 0, 0, 544, 544, 442, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 545, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 491, 492, 493, 494, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 492, 492, 494, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 546, 546, 494, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 547, 546, 546, 494, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 548, 549, 368, 550, 0, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 274, 0, 368, 368, 0, 161, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 161, 0, 0, 0, 0, 498, 0, 0, 0, 0, 0], [0, 0, 0, 495, 0, 496, 368, 0, 161, 0, 162, 0, 0, 498, 0, 0, 0, 0, 0], [0, 0, 0, 495, 0, 368, 368, 0, 161, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 551, 500, 552, 552, 22, 23, 0, 24, 0, 0, 0, 504, 0, 0, 27, 0], [0, 553, 0, 0, 0, 76, 554, 0, 23, 0, 24, 0, 0, 0, 78, 0, 553, 0, 0], [0, 0, 0, 0, 555, 80, 80, 22, 23, 0, 0, 0, 0, 503, 555, 0, 0, 0, 0], [0, 0, 0, 499, 500, 501, 552, 22, 23, 0, 24, 0, 0, 503, 504, 0, 0, 27, 0], [0, 0, 0, 499, 500, 552, 552, 22, 23, 0, 24, 0, 0, 0, 504, 0, 0, 27, 0], [0, 556, 90, 0, 0, 76, 554, 0, 23, 0, 24, 0, 0, 0, 78, 0, 556, 0, 0], [0, 448, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 448, 0, 0], [0, 448, 0, 505, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 448, 0, 0], [0, 0, 0, 551, 500, 552, 552, 22, 23, 0, 24, 0, 0, 0, 504, 0, 0, 27, 0], [0, 0, 0, 0, 555, 80, 80, 22, 23, 0, 0, 0, 0, 510, 555, 0, 0, 0, 0], [0, 0, 0, 507, 500, 508, 552, 22, 23, 0, 24, 0, 0, 510, 504, 0, 0, 27, 0], [0, 0, 0, 507, 500, 552, 552, 22, 23, 0, 24, 0, 0, 0, 504, 0, 0, 27, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 76, 0, 0], [0, 0, 0, 557, 513, 558, 558, 32, 33, 0, 34, 0, 0, 0, 517, 0, 0, 37, 0], [0, 559, 0, 0, 0, 97, 560, 0, 33, 0, 34, 0, 0, 0, 99, 0, 559, 0, 0], [0, 0, 0, 0, 561, 101, 101, 32, 33, 0, 0, 0, 0, 516, 561, 0, 0, 0, 0], [0, 0, 0, 512, 513, 514, 558, 32, 33, 0, 34, 0, 0, 516, 517, 0, 0, 37, 0], [0, 0, 0, 512, 513, 558, 558, 32, 33, 0, 34, 0, 0, 0, 517, 0, 0, 37, 0], [0, 562, 111, 0, 0, 97, 560, 0, 33, 0, 34, 0, 0, 0, 99, 0, 562, 0, 0], [0, 457, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 457, 0, 0], [0, 457, 0, 518, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 457, 0, 0], [0, 0, 0, 557, 513, 558, 558, 32, 33, 0, 34, 0, 0, 0, 517, 0, 0, 37, 0], [0, 0, 0, 0, 561, 101, 101, 32, 33, 0, 0, 0, 0, 523, 561, 0, 0, 0, 0], [0, 0, 0, 520, 513, 521, 558, 32, 33, 0, 34, 0, 0, 523, 517, 0, 0, 37, 0], [0, 0, 0, 520, 513, 558, 558, 32, 33, 0, 34, 0, 0, 0, 517, 0, 0, 37, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 97, 0, 0], [0, 0, 0, 0, 465, 526, 526, 8, 9, 0, 10, 0, 0, 0, 469, 0, 0, 16, 0], [0, 0, 0, 0, 529, 44, 44, 8, 9, 0, 0, 0, 0, 0, 529, 0, 0, 0, 0], [0, 0, 0, 563, 66, 564, 565, 8, 9, 0, 10, 0, 0, 566, 68, 0, 0, 16, 0], [0, 527, 0, 567, 0, 123, 123, 8, 9, 0, 10, 0, 0, 0, 42, 0, 527, 0, 0], [0, 527, 0, 0, 0, 121, 568, 0, 9, 0, 10, 0, 0, 0, 42, 0, 527, 0, 0], [0, 0, 0, 569, 66, 570, 571, 8, 9, 0, 10, 0, 0, 572, 68, 0, 0, 16, 0], [0, 0, 0, 573, 532, 574, 574, 57, 58, 0, 59, 0, 0, 0, 536, 0, 0, 62, 0], [0, 575, 0, 0, 0, 137, 576, 0, 58, 0, 59, 0, 0, 0, 139, 0, 575, 0, 0], [0, 0, 0, 0, 577, 141, 141, 57, 58, 0, 0, 0, 0, 535, 577, 0, 0, 0, 0], [0, 0, 0, 531, 532, 533, 574, 57, 58, 0, 59, 0, 0, 535, 536, 0, 0, 62, 0], [0, 0, 0, 531, 532, 574, 574, 57, 58, 0, 59, 0, 0, 0, 536, 0, 0, 62, 0], [0, 578, 151, 0, 0, 137, 576, 0, 58, 0, 59, 0, 0, 0, 139, 0, 578, 0, 0], [0, 479, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 479, 0, 0], [0, 479, 0, 537, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 479, 0, 0], [0, 0, 0, 573, 532, 574, 574, 57, 58, 0, 59, 0, 0, 0, 536, 0, 0, 62, 0], [0, 0, 0, 0, 577, 141, 141, 57, 58, 0, 0, 0, 0, 542, 577, 0, 0, 0, 0], [0, 0, 0, 539, 532, 540, 574, 57, 58, 0, 59, 0, 0, 542, 536, 0, 0, 62, 0], [0, 0, 0, 539, 532, 574, 574, 57, 58, 0, 59, 0, 0, 0, 536, 0, 0, 62, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 137, 0, 0], [0, 0, 0, 0, 0, 0, 0, 442, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 439, 0, 0], [0, 0, 0, 0, 0, 579, 579, 494, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 580, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 549, 368, 550, 0, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 368, 368, 0, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 581, 0, 0, 0, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 500, 552, 552, 22, 23, 0, 24, 0, 0, 0, 504, 0, 0, 27, 0], [0, 0, 0, 0, 555, 80, 80, 22, 23, 0, 0, 0, 0, 0, 555, 0, 0, 0, 0], [0, 0, 0, 582, 91, 583, 584, 22, 23, 0, 24, 0, 0, 585, 93, 0, 0, 27, 0], [0, 553, 0, 586, 0, 177, 177, 22, 23, 0, 24, 0, 0, 0, 78, 0, 553, 0, 0], [0, 553, 0, 0, 0, 175, 587, 0, 23, 0, 24, 0, 0, 0, 78, 0, 553, 0, 0], [0, 0, 0, 588, 91, 589, 590, 22, 23, 0, 24, 0, 0, 591, 93, 0, 0, 27, 0], [0, 0, 0, 0, 513, 558, 558, 32, 33, 0, 34, 0, 0, 0, 517, 0, 0, 37, 0], [0, 0, 0, 0, 561, 101, 101, 32, 33, 0, 0, 0, 0, 0, 561, 0, 0, 0, 0], [0, 0, 0, 592, 112, 593, 594, 32, 33, 0, 34, 0, 0, 595, 114, 0, 0, 37, 0], [0, 559, 0, 596, 0, 203, 203, 32, 33, 0, 34, 0, 0, 0, 99, 0, 559, 0, 0], [0, 559, 0, 0, 0, 201, 597, 0, 33, 0, 34, 0, 0, 0, 99, 0, 559, 0, 0], [0, 0, 0, 598, 112, 599, 600, 32, 33, 0, 34, 0, 0, 601, 114, 0, 0, 37, 0], [0, 0, 0, 602, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 16, 0], [0, 0, 0, 0, 165, 44, 44, 8, 9, 0, 0, 0, 0, 566, 165, 0, 0, 0, 0], [0, 0, 0, 563, 66, 564, 67, 8, 9, 0, 10, 0, 0, 566, 68, 0, 0, 16, 0], [0, 0, 0, 563, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 16, 0], [0, 527, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 527, 0, 0], [0, 527, 0, 567, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 527, 0, 0], [0, 0, 0, 602, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 16, 0], [0, 0, 0, 0, 165, 44, 44, 8, 9, 0, 0, 0, 0, 572, 165, 0, 0, 0, 0], [0, 0, 0, 569, 66, 570, 67, 8, 9, 0, 10, 0, 0, 572, 68, 0, 0, 16, 0], [0, 0, 0, 569, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 16, 0], [0, 0, 0, 0, 532, 574, 574, 57, 58, 0, 59, 0, 0, 0, 536, 0, 0, 62, 0], [0, 0, 0, 0, 577, 141, 141, 57, 58, 0, 0, 0, 0, 0, 577, 0, 0, 0, 0], [0, 0, 0, 603, 152, 604, 605, 57, 58, 0, 59, 0, 0, 606, 154, 0, 0, 62, 0], [0, 575, 0, 607, 0, 248, 248, 57, 58, 0, 59, 0, 0, 0, 139, 0, 575, 0, 0], [0, 575, 0, 0, 0, 246, 608, 0, 58, 0, 59, 0, 0, 0, 139, 0, 575, 0, 0], [0, 0, 0, 609, 152, 610, 611, 57, 58, 0, 59, 0, 0, 612, 154, 0, 0, 62, 0], [0, 0, 0, 0, 0, 0, 0, 494, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 491, 0, 0], [0, 0, 0, 0, 0, 0, 613, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 614, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 27, 0], [0, 0, 0, 0, 194, 80, 80, 22, 23, 0, 0, 0, 0, 585, 194, 0, 0, 0, 0], [0, 0, 0, 582, 91, 583, 92, 22, 23, 0, 24, 0, 0, 585, 93, 0, 0, 27, 0], [0, 0, 0, 582, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 27, 0], [0, 553, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 553, 0, 0], [0, 553, 0, 586, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 553, 0, 0], [0, 0, 0, 614, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 27, 0], [0, 0, 0, 0, 194, 80, 80, 22, 23, 0, 0, 0, 0, 591, 194, 0, 0, 0, 0], [0, 0, 0, 588, 91, 589, 92, 22, 23, 0, 24, 0, 0, 591, 93, 0, 0, 27, 0], [0, 0, 0, 588, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 27, 0], [0, 0, 0, 615, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 37, 0], [0, 0, 0, 0, 220, 101, 101, 32, 33, 0, 0, 0, 0, 595, 220, 0, 0, 0, 0], [0, 0, 0, 592, 112, 593, 113, 32, 33, 0, 34, 0, 0, 595, 114, 0, 0, 37, 0], [0, 0, 0, 592, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 37, 0], [0, 559, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 559, 0, 0], [0, 559, 0, 596, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 559, 0, 0], [0, 0, 0, 615, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 37, 0], [0, 0, 0, 0, 220, 101, 101, 32, 33, 0, 0, 0, 0, 601, 220, 0, 0, 0, 0], [0, 0, 0, 598, 112, 599, 113, 32, 33, 0, 34, 0, 0, 601, 114, 0, 0, 37, 0], [0, 0, 0, 598, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 37, 0], [0, 0, 0, 0, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 16, 0], [0, 0, 0, 616, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 62, 0], [0, 0, 0, 0, 265, 141, 141, 57, 58, 0, 0, 0, 0, 606, 265, 0, 0, 0, 0], [0, 0, 0, 603, 152, 604, 153, 57, 58, 0, 59, 0, 0, 606, 154, 0, 0, 62, 0], [0, 0, 0, 603, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 62, 0], [0, 575, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 575, 0, 0], [0, 575, 0, 607, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 575, 0, 0], [0, 0, 0, 616, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 62, 0], [0, 0, 0, 0, 265, 141, 141, 57, 58, 0, 0, 0, 0, 612, 265, 0, 0, 0, 0], [0, 0, 0, 609, 152, 610, 153, 57, 58, 0, 59, 0, 0, 612, 154, 0, 0, 62, 0], [0, 0, 0, 609, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 62, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 549, 0, 0], [0, 0, 0, 0, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 27, 0], [0, 0, 0, 0, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 37, 0], [0, 0, 0, 0, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 62, 0]];
var accepting = [false, true, true, true, true, true, false, false, true, true, true, true, true, true, true, true, true, true, true, true, false, true, true, true, true, true, true, true, true, true, false, true, true, true, true, true, true, true, true, true, true, true, false, true, false, true, true, false, false, true, true, true, true, true, true, false, false, true, true, true, true, true, true, true, true, true, true, false, true, true, false, true, true, true, false, true, true, true, false, true, false, true, true, false, false, true, true, true, true, true, true, true, false, true, true, false, true, true, true, false, true, false, true, true, false, false, true, true, true, true, true, true, true, false, true, true, true, false, true, true, true, false, true, false, true, true, false, false, false, true, true, false, false, true, true, true, true, true, true, false, true, false, true, true, false, false, true, true, true, true, true, true, true, false, true, true, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, false, true, true, true, false, true, false, true, true, false, false, false, true, true, false, false, true, true, true, false, true, true, true, true, true, true, false, true, true, true, false, true, false, true, true, false, false, false, true, true, false, false, true, true, true, false, true, true, true, true, true, false, true, true, true, true, true, false, true, true, false, false, false, false, true, true, false, false, true, true, true, false, true, true, true, false, true, false, true, true, false, false, false, true, true, false, false, true, true, true, false, true, true, true, true, false, true, false, true, true, true, true, true, true, true, true, true, false, true, true, true, true, true, false, true, true, false, false, false, false, true, true, false, false, true, true, true, false, true, true, true, true, true, false, true, true, false, false, false, false, true, true, false, false, true, true, true, true, false, true, true, true, true, true, true, false, true, true, false, false, false, false, true, false, true, false, true, true, true, true, true, false, true, true, false, false, false, false, true, true, false, false, true, true, true, false, true, true, false, false, true, false, true, true, false, true, true, false, true, true, false, true, true, true, true, true, true, false, true, true, false, false, false, false, true, false, true, true, false, true, true, true, true, true, true, false, true, true, false, false, false, false, true, false, true, false, true, true, true, true, false, false, false, true, true, false, true, true, true, true, true, true, false, true, true, false, false, false, false, true, false, true, false, true, true, false, false, true, true, false, false, true, true, true, false, true, false, true, true, true, true, false, false, false, true, false, true, true, true, true, false, false, false, true, true, false, true, true, true, true, true, true, false, true, true, false, true, false, true, true, true, true, false, false, false, false, false, false, false, true, true, false, false, true, true, false, true, true, true, true, false, true, true, true, true, true, true, false, true, true, false, true, true, false, true, true, true, true, true, true, false, true, true, false, true, false, true, true, true, true, true, true, false, true, true, true, true, true, true, false, true, true, false, false, false, false, false, true, true, false, true, false, true, true, true, true, true, false, true, true, true, true, true, false, true, true, true, true, true, false, true, true, true, false, true, true, true, true, false, false, false, true, false, true, true, true, true, true, false, true, true, true, false, true, true, true, true, true, false, true, true, true, true, false, true, true, true, true, true, false, true, true, false, true, true, true];
var tags = [[], ["broken_cluster"], ["consonant_syllable"], ["vowel_syllable"], ["broken_cluster"], ["broken_cluster"], [], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["standalone_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["consonant_syllable"], ["broken_cluster"], ["symbol_cluster"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], [], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["broken_cluster"], ["broken_cluster"], ["consonant_syllable", "broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["symbol_cluster"], [], ["symbol_cluster"], ["symbol_cluster"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], [], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], [], [], [], ["broken_cluster"], ["broken_cluster"], [], [], ["broken_cluster"], ["broken_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["symbol_cluster"], ["symbol_cluster"], ["symbol_cluster"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], [], [], [], ["consonant_syllable"], ["consonant_syllable"], [], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], [], [], ["vowel_syllable"], ["vowel_syllable"], [], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], [], [], [], [], ["broken_cluster"], ["broken_cluster"], [], [], ["broken_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], [], [], ["standalone_cluster"], ["standalone_cluster"], [], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["broken_cluster"], ["symbol_cluster"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], [], [], [], [], ["consonant_syllable"], ["consonant_syllable"], [], [], ["consonant_syllable"], ["consonant_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], [], [], [], ["vowel_syllable"], ["vowel_syllable"], [], [], ["vowel_syllable"], ["vowel_syllable"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], [], [], [], [], ["broken_cluster"], [], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], [], [], [], ["standalone_cluster"], ["standalone_cluster"], [], [], ["standalone_cluster"], ["standalone_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], [], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], [], [], [], [], ["consonant_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], [], [], [], ["vowel_syllable"], [], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], [], [], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], [], [], [], ["standalone_cluster"], [], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], [], [], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], [], [], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], [], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], [], [], [], [], [], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], [], [], [], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], [], [], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["broken_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], ["consonant_syllable"], ["vowel_syllable"], ["standalone_cluster"]];
var indicMachine = {
	stateTable: stateTable,
	accepting: accepting,
	tags: tags
};

var categories = ["O", "IND", "S", "GB", "B", "FM", "CGJ", "VMAbv", "VMPst", "VAbv", "VPst", "CMBlw", "VPre", "VBlw", "H", "VMBlw", "CMAbv", "MBlw", "CS", "R", "SUB", "MPst", "MPre", "FAbv", "FPst", "FBlw", "null", "SMAbv", "SMBlw", "VMPre", "ZWNJ", "ZWJ", "WJ", "M", "VS", "N", "HN", "MAbv"];
var decompositions$1 = { "2507": [2503, 2494], "2508": [2503, 2519], "2888": [2887, 2902], "2891": [2887, 2878], "2892": [2887, 2903], "3018": [3014, 3006], "3019": [3015, 3006], "3020": [3014, 3031], "3144": [3142, 3158], "3264": [3263, 3285], "3271": [3270, 3285], "3272": [3270, 3286], "3274": [3270, 3266], "3275": [3270, 3266, 3285], "3402": [3398, 3390], "3403": [3399, 3390], "3404": [3398, 3415], "3546": [3545, 3530], "3548": [3545, 3535], "3549": [3545, 3535, 3530], "3550": [3545, 3551], "3635": [3661, 3634], "3763": [3789, 3762], "3955": [3953, 3954], "3957": [3953, 3956], "3958": [4018, 3968], "3959": [4018, 3953, 3968], "3960": [4019, 3968], "3961": [4019, 3953, 3968], "3969": [3953, 3968], "6971": [6970, 6965], "6973": [6972, 6965], "6976": [6974, 6965], "6977": [6975, 6965], "6979": [6978, 6965], "69934": [69937, 69927], "69935": [69938, 69927], "70475": [70471, 70462], "70476": [70471, 70487], "70843": [70841, 70842], "70844": [70841, 70832], "70846": [70841, 70845], "71098": [71096, 71087], "71099": [71097, 71087] };
var stateTable$1 = [[0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [2, 2, 3, 4, 4, 5, 0, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 0, 17, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 2, 0, 0, 24, 0, 25], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 26, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 27, 28, 0, 0, 0, 0, 0, 27, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 39, 0, 0, 47], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 0, 0, 0, 0, 0, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 0, 7, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 0, 0, 12, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 0, 9, 0, 0, 0, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 10, 11, 12, 13, 14, 0, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 0, 11, 12, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 0, 9, 0, 0, 12, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 18, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 0, 7, 0, 0, 0, 0, 0, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 0, 11, 12, 0, 14, 0, 0, 0, 0, 0, 11, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 4, 4, 5, 0, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 48, 11, 12, 13, 14, 48, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 49, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 0, 11, 12, 0, 14, 0, 16, 0, 0, 0, 11, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 21, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 21, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 0, 0, 0, 0, 0, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 50, 0, 51, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 0, 11, 12, 0, 14, 0, 16, 0, 0, 0, 11, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 27, 28, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 28, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 0, 0, 0, 0, 0, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 0, 31, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 0, 0, 36, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 0, 33, 0, 0, 0, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 34, 35, 36, 37, 38, 0, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 47], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 0, 35, 36, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 0, 33, 0, 0, 36, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 41, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 0, 31, 0, 0, 0, 0, 0, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 47], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 0, 35, 36, 0, 38, 0, 0, 0, 0, 0, 35, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 52, 35, 36, 37, 38, 52, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 53, 0, 0, 47], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 0, 35, 36, 0, 38, 0, 40, 0, 0, 0, 35, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 47], [0, 0, 0, 0, 0, 29, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 44, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 44, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 0, 0, 0, 0, 0, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 0, 35, 36, 0, 38, 0, 40, 0, 0, 0, 35, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 48, 11, 12, 13, 14, 0, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 48, 11, 12, 13, 14, 48, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 51, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 54, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 52, 35, 36, 37, 38, 0, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 47], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 52, 35, 36, 37, 38, 52, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 47], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 50, 0, 51, 0]];
var accepting$1 = [false, true, true, true, true, true, true, true, true, true, true, true, true, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true];
var tags$1 = [[], ["broken_cluster"], ["independent_cluster"], ["symbol_cluster"], ["standard_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["numeral_cluster"], ["broken_cluster"], ["independent_cluster"], ["symbol_cluster"], ["symbol_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["virama_terminated_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["broken_cluster"], ["broken_cluster"], ["numeral_cluster"], ["number_joiner_terminated_cluster"], ["standard_cluster"], ["standard_cluster"], ["numeral_cluster"]];
var useData = {
	categories: categories,
	decompositions: decompositions$1,
	stateTable: stateTable$1,
	accepting: accepting$1,
	tags: tags$1
};

// Cateories used in the OpenType spec:
// https://www.microsoft.com/typography/otfntdev/devanot/shaping.aspx
var CATEGORIES = {
  X: 1 << 0,
  C: 1 << 1,
  V: 1 << 2,
  N: 1 << 3,
  H: 1 << 4,
  ZWNJ: 1 << 5,
  ZWJ: 1 << 6,
  M: 1 << 7,
  SM: 1 << 8,
  VD: 1 << 9,
  A: 1 << 10,
  Placeholder: 1 << 11,
  Dotted_Circle: 1 << 12,
  RS: 1 << 13, // Register Shifter, used in Khmer OT spec.
  Coeng: 1 << 14, // Khmer-style Virama.
  Repha: 1 << 15, // Atomically-encoded logical or visual repha.
  Ra: 1 << 16,
  CM: 1 << 17, // Consonant-Medial.
  Symbol: 1 << 18 // Avagraha, etc that take marks (SM,A,VD).
};

// Visual positions in a syllable from left to right.
var POSITIONS = {
  Start: 1 << 0,

  Ra_To_Become_Reph: 1 << 1,
  Pre_M: 1 << 2,
  Pre_C: 1 << 3,

  Base_C: 1 << 4,
  After_Main: 1 << 5,

  Above_C: 1 << 6,

  Before_Sub: 1 << 7,
  Below_C: 1 << 8,
  After_Sub: 1 << 9,

  Before_Post: 1 << 10,
  Post_C: 1 << 11,
  After_Post: 1 << 12,

  Final_C: 1 << 13,
  SMVD: 1 << 14,

  End: 1 << 15
};

var CONSONANT_FLAGS = CATEGORIES.C | CATEGORIES.Ra | CATEGORIES.CM | CATEGORIES.V | CATEGORIES.Placeholder | CATEGORIES.Dotted_Circle;
var JOINER_FLAGS = CATEGORIES.ZWJ | CATEGORIES.ZWNJ;
var HALANT_OR_COENG_FLAGS = CATEGORIES.H | CATEGORIES.Coeng;

var INDIC_CONFIGS = {
  Default: {
    hasOldSpec: false,
    virama: 0,
    basePos: 'Last',
    rephPos: POSITIONS.Before_Post,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },

  Devanagari: {
    hasOldSpec: true,
    virama: 0x094D,
    basePos: 'Last',
    rephPos: POSITIONS.Before_Post,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },

  Bengali: {
    hasOldSpec: true,
    virama: 0x09CD,
    basePos: 'Last',
    rephPos: POSITIONS.After_Sub,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },

  Gurmukhi: {
    hasOldSpec: true,
    virama: 0x0A4D,
    basePos: 'Last',
    rephPos: POSITIONS.Before_Sub,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },

  Gujarati: {
    hasOldSpec: true,
    virama: 0x0ACD,
    basePos: 'Last',
    rephPos: POSITIONS.Before_Post,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },

  Oriya: {
    hasOldSpec: true,
    virama: 0x0B4D,
    basePos: 'Last',
    rephPos: POSITIONS.After_Main,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },

  Tamil: {
    hasOldSpec: true,
    virama: 0x0BCD,
    basePos: 'Last',
    rephPos: POSITIONS.After_Post,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },

  Telugu: {
    hasOldSpec: true,
    virama: 0x0C4D,
    basePos: 'Last',
    rephPos: POSITIONS.After_Post,
    rephMode: 'Explicit',
    blwfMode: 'Post_Only'
  },

  Kannada: {
    hasOldSpec: true,
    virama: 0x0CCD,
    basePos: 'Last',
    rephPos: POSITIONS.After_Post,
    rephMode: 'Implicit',
    blwfMode: 'Post_Only'
  },

  Malayalam: {
    hasOldSpec: true,
    virama: 0x0D4D,
    basePos: 'Last',
    rephPos: POSITIONS.After_Main,
    rephMode: 'Log_Repha',
    blwfMode: 'Pre_And_Post'
  },

  // Handled by UniversalShaper
  // Sinhala: {
  //   hasOldSpec: false,
  //   virama: 0x0DCA,
  //   basePos: 'Last_Sinhala',
  //   rephPos: POSITIONS.After_Main,
  //   rephMode: 'Explicit',
  //   blwfMode: 'Pre_And_Post'
  // },

  Khmer: {
    hasOldSpec: false,
    virama: 0x17D2,
    basePos: 'First',
    rephPos: POSITIONS.Ra_To_Become_Reph,
    rephMode: 'Vis_Repha',
    blwfMode: 'Pre_And_Post'
  }
};

// Additional decompositions that aren't in Unicode
var INDIC_DECOMPOSITIONS = {
  // Khmer
  0x17BE: [0x17C1, 0x17BE],
  0x17BF: [0x17C1, 0x17BF],
  0x17C0: [0x17C1, 0x17C0],
  0x17C4: [0x17C1, 0x17C4],
  0x17C5: [0x17C1, 0x17C5]
};

var _class$6;
var _temp$2;
var decompositions = useData.decompositions;

var trie$1 = new UnicodeTrie(Buffer("ABEAAAAAAAAAANhgAWYPmfDtnXuMXFUdx+/uzs7M7szudAtECGJRIMRQbUAithQWkGAKiVhNpFVRRAmIQVCDkDYICGotIA9BTCz8IeUviv7BQ2PBtBIRLBBQIWAUsKg1BKxRAqIgfs/cc+aeOXPej3tnZX7JJ/dxzj3nd36/8753Z5fUsuxgsAwcAU4Gp4BPgM+Cd4P3RjieDs4GXwLrHJ5bDy4DG8A14LvgZrAZbAF3gns0z18ALgY/B78C94NHwBPgabAE/AX8DbwM5sF/QX0yD5vFcU/wVnAgWAoOAyvAceBE8CGwBpwGzgJfAF8BXwXfAFeC68EmsBlsAXeCreA+8CB4DDwF/gh2gd3gFfAGmKxn2QzYC+wHDgRLweFgJTgWrKrnuq/GcQ04jV6fheN54EJwEbgcXAG+Q8O/j+Mt4DZwB9haz8t9Hz3a8iCN/xiOvwRP0evH6fE68AzOH+Ke2eWYhw3PcGnuxvkr4A3QaGRZB7wFLAEHg2XgiEZ/fHKcp/ceBh/A+cngFPCpRm6vM3E8l8a5gN67GMdvgqsbeX2ap9yI601gM7gN3AG20mfuo8cdOP6GpvdUg9oKxz839GV90RDO2/glxN1B790NXsN1rZll7WYRdw+c70uvTwIHNAfTO0RyL5TDmnnbc3lmRQI9UnM0dD5eovfz4FpJ/BNpXNYWV+N6Lfg0hY97JK1vn+Pur9DoQur2F7m436bHDUK8C5t5/8vruo4+97WmXG+GLmzEiBF+PDwEOowYMWLEiBEjRoxYeBw5BDqIPEfXut9yWN+vVNxfrnnmWqR/PdgENoMt4E5wD9gOHgCPgifBs2BXM99b2o3jP8F/wMRUlrXAHNgHvH0q3895J46HguXgWHAGLctmLv9VuL96qnp7jxgxYsSbCbJvuRZ97/tqxT59VVRtixEjRsThBG7OSt5zzoPT0M+cBc4T5noXOs79TqLHeZrHUeCSqeJ96gacXy2kecNU8V6Hh7yXuQlhtw7B/PO1RTkr52Aj8JNFZjYg3gOKuC/g/v6Ls2wNuAY8urg//PcIb+6RZXuDNeCS6SzbBrJWlh0DLiFHco8ed9IjzzvaWfa9sZzTcf6D9mCcnbg3PlNcH4fzS8F2MDaLdQG4dLZIJxbbaZqv4ri8k58f3+mPs66T6/TTzqDeI0aMGDGiHP5dcR8ce/xxYcWi6vOfr725uRzcjnngXVOD61Hync+9uL+Nmyfej/NHpvL56A5Jeuz7uyfo+pqcPz2Vf1NH0ttJ03pekt8SmuY/EPYy9zzbN319ym/9TL6ZIt9MHCXRdxJtoAkWTRdz472n87D9cTwYLJvuz++I6WIePo/zE8AHp4v8WLyP0nufnM6/+zoDx8+DL08P6r9+urheRtO+jD6/cdrsx3mqu8w+xH4PScKIXa5D2jeCm8Et4DbwI/BjcC/4BXgI/Bb8DuwEu8Bu8Ap4A9RaRZptnO8J9gUHgEPAoWA5OLY1qMO90GEV7q+mYWtxPBWcIYnL4p+DsPNbxfVFOP86uAr8DNc34HgTDb8Vx9sVaRFI/LtagzYjnCqpb908EX87eBA8Bh4Hf2jle/9/wvGFVv787rrZZy8h7qtgDOuFOmiBuXYRvg/O9wMHgXeB97SLspk4sq0OI/q9v13+ek+sh3zYSRp9jrYorw9ll1/GRzR+KotYZSHf8laVP2lvpA/8OGdPMk59hqtXZ+L8nHbxvWwqO65ryu+fT3VZz+l4dET7L0R072ljsMyzTpaJqQxsbL8M9WajY789DO85XMp/Dcp3Qztdn+9qf/a97ZWK8PXc3G+TpC/nv8Mncy7ZvICF302P5O+aNiOtLdTXd+D4Q7DVwfcvWvx9zTEJ/o5iG3R8YAjGNFseha5PGuZKz7b7xxXbOrXMcu5eJSo//rXdH/73Enz6L1q/X+fyIu8wZGtNBmkjkzNZNgP2AvuBg2bysKUzduXn/66JtNeN4PCZvO0/x7Ujdn4VnYOvRJzjZ/I+9sQZeftX2Tc1RPcPz/Tf4/si0g+t5Mq+kfZjZL34Mc5ul3PPnE7TOxvHK2qDaZ+L++db2HyYqMo/qVnb/P8uH8/rmnFxR0k6DCu/rjj/RxT7KGUSWgbd+LMQuEgYB1zsk2qtvJD8v5AhdfdttbEunSxbcJD9Zf7chqp1Hlbe7FK1/aPVTfp7FgtC1yGGiSncFK/DhZvi+epZta0WWjlsfDZMyPRdSPrryqSSKnXx1bkq/Ye9TlRpk7Lrjq1UrfdC9X+MtKqwP6+3a/4pJFUZF0pZZpv91MYjMBaRRXbxpho5zQmUY3F+Pt4o7rvQrBXPdm00TaE24uMadaM2meLSI7iu071t3er3b6ZLi8JEde3qw+6zGv+ycF5kaRBh/m1T/7Yl/mMyTuMwadP4xL9ifjJpNwbvDZRJ8G8vnqV/Wf12aa/kyOdl69+BspTsXzGueE6E+JfZnvmXIfNPW+FfXkjb1YmqPNpnLP3b61fHCj/X5tzGANf2y3yqvC7Jv7btV4TVbdammI9l/g0dS5lNxLrk2j9r8xjjxhBQnygg0lgg/bOrfyct+udJi/Yrk0lFnxC7f+5kRbsNmcexfrubt0X/rGvLqrGSnYv3ZPHEe8r7lvMvUfi2LOu/2dg8LrRtQt2yfcv8r5IU70VkIs6nbebUXf0M/o7Znl39Sdoz+X1oEb5N8ffF67qhPfPP6eoUbxf+GRf/6sRnvaSdmw+Bf1VxmbD+2sa//DU7t/Gv2PfKpKdrBP92Ojk+IvqX16ks/2qxbL8EZnc2HqsgYuqPuzZV+I3RbujbDm+T0PmWCVO/5jqftp1zy+wSA6s0JWtp2z5e1oZV+yMsjB3ZXolsv0Ulrv01v3/iKrF94Qtbt9siCnmeb6fjjf59KnLk1xaEbvtvFnFirGvEOqmycQrbm/IMsXd3P28uh4nM3swXRER717OiX8kc7K2qqyn2p3maFGU/aruP5VCv+PraoTYU8yUmmbDwcYo6pusnM486xdoga4dkPCb1pK7Sfc6ebvkd4qeAtQcd/N63bB3lU3dlUnUf38VyvqCqK7JxlNSd7lydrDlm+/uqHiRvl30Nrp/n9zpkZRjoJ3V1diyP05rIYXHYs+w+D5+WMS8b5gZtKcuX0KT5d/WwtB97VnyvY6rjMukI56HI0rFJPwt8PjT/1OXzSbcMeEmdh294qvKK4rNu7j4n3LNZg8TKXwafv025U+XvKjHsT8Q7/7LGaJt9lAh7Asz3uv0XEX6t0duDoWN/93wmh92XpUHmCKb9GALbG+rZP3AfNbQPKKv/jpF/bP0JXfuW1QYk7dhljcyvk5mw+933Hpo1g26PQ2ZP6zVmTJt47P25jncD9vPwGS+q9QS/V6RaY8j8K8LmvUr9HfYCpH5OWL9lZY+Sv6pesHCJHbtrf9k6etZvf0G1L0ja4cAe1UT/s3zdCe3/Q5/n372wMc97/E1Qh0Tbmfwh3m/V9On72tNnrCF1sJkVe1EyXMdBa7+lHMsk44zMF6St9e2djNnbm8ybpHkq+gbbemMaH0UZmD8obKGrk7r+nt+3bE7o83YZp/vqOKdv6PzJNN6mTJsI/51XR7i2ZrGA5B6zFwnjzxmqPjaGfW3tZNrz1eljq29mOOqeCfF/irRt87PNw0uXSVAvrmOMNT569MptsYaV0sic/wbY13e8hPrb9K2ySUJ0j6G/Lu0U4qpTrR23jMp6m5hU+YTaWCeh9aIsm/rqUHV4bFv42kgnZdfH1PUj1D7DVH9d8khRN1zFRl/+/TW//qxL1uH83+mk3H+SvRtS2TDU90nX2TpM6/1xzZpZtoYdK763dqlz0f6uNeFehcs+H/nbGP77MpX06n/ofpzP+tVmTUvRtVuX/cjS67OE5kRBrxyJ+w/dPo7r+9cO1160e3gqu0S2uW7PjN/L6ns/UfMf10Lai87frJ+3KndAfc8yTf1M3T4s6qm4/yh7/2GSkG8UMw//DvRLgbYZSEOxr0LCWvRdjfh9XGzfqN4NivfZd7rsmFp08zmbssrKJEuTfVMZopdpbuwSrhNv3/N2s+0PDG3KNB6RMrFvJHv6B85HXObAoWsd3zm3i+6uZYytv+5+pohbpo6+tpZJFfmGlrcMf4c8b1Pe2OUIsaXJrinCTfaxtZOt+NYnU3hIfQlN20Z/1+dt7JaqLsbIzycNWZmrlNg2Dc2/LJ1T+T6WrrYSml4Ku7ik7yIx2opJD51vU9UfVRmrqL8u/olZj0PyCLV5irxcdKoi/6rKb8qTrHsnhW9jyZH/nSpeWDzxd9769uQ016lgUuf2pAfKPhu2FpfZL2Yb9snLNl/fNIepXaUsj4vNXCXUZ75px8ojNP8UPvAta2g6fb+F1ckZuneshv1vGXXDeyRRrN/bBPS1Jul+l+7zW86R7Wv63WXyDpt/RxraRjvC+TC3O61/Sqj/prag8x372yQivn+XwudrI2X2E2KdtJEov52e0L+uv4FO3p/rvssgsL8F4d/z9PzlWS94m8fqS3361Fi+6qaVYHwi9Yz4iH2fobIj+45cpz/TUaarr/4+z+vaWtVtyAX2d1LG8W9C3f+F1mnf36/k4w3YPrLv+XBVXCJs3cr+n4MKJuLv/fN9GhNdXVP5pJMN9vFi3rpv3/r8Ywg3SYp66zNOsO8QGcxPpnmRS/1mvmJjju3v7absI2xspQrvs1dNbjOj/wP7h1RlZyKGy8occ408UL8En4v6xfC/K3z52XzJd62T8vuZGGsxo/6O46ntmNqqFb/jps2/hHV4rPKH0svT4pstU7t2tZ9u/ZdqbJL1MwP6O86Fyt4jYaIrGz9mjEt8lFL4PtVE6votG2P6fpdf/GZRse7s3bf4BtSl/DIbKMctx++Z+8o6K6z9FPOwKsRmXiaNl7C+6NYRpjlbqG1j72f49qsuY4brd/amb4ZVc8TQ+sSH985LrEe8iPWJnfPrJRbWbb+dwn4x6o+r/aS2S7w3qWt//LnYz2ntE0vH1uDcyKatx1rH+EiMPEN1SZG/iz6+9o01Rob6O7Q+xLZ1jHobK61U+pWVvo2EpuWqzzD6Poa+pvhli0wn8Zq/72Mzm2d90o5VN1x9ZKuzbTgvqWwUIin8FSpl1CXXvFRxU0iozVPYJDRtF3uFphn6XAyJUUdD7SjTJ8v6n9fVbVObkKWp001lc9VRlqdOf5v0ZM+bymdbfp1NfG0bq27Y5JMyfxeJkU6o/inKH8O2Zfgidb6h/g3VJ7QcVbWL0Pxt6rlrPqa4KfQ25a2zl4/E8GdM/4fK/wA=","base64"));
var stateMachine = new StateMachine(indicMachine);

/**
 * The IndicShaper supports indic scripts e.g. Devanagari, Kannada, etc.
 * Based on code from Harfbuzz: https://github.com/behdad/harfbuzz/blob/master/src/hb-ot-shape-complex-indic.cc
 */
var IndicShaper = (_temp$2 = _class$6 = function (_DefaultShaper) {
  _inherits(IndicShaper, _DefaultShaper);

  function IndicShaper() {
    _classCallCheck(this, IndicShaper);

    return _possibleConstructorReturn(this, _DefaultShaper.apply(this, arguments));
  }

  IndicShaper.planFeatures = function planFeatures(plan) {
    plan.addStage(setupSyllables);

    plan.addStage(['locl', 'ccmp']);

    plan.addStage(initialReordering);

    plan.addStage('nukt');
    plan.addStage('akhn');
    plan.addStage('rphf', false);
    plan.addStage('rkrf');
    plan.addStage('pref', false);
    plan.addStage('blwf', false);
    plan.addStage('abvf', false);
    plan.addStage('half', false);
    plan.addStage('pstf', false);
    plan.addStage('vatu');
    plan.addStage('cjct');
    plan.addStage('cfar', false);

    plan.addStage(finalReordering);

    plan.addStage({
      local: ['init'],
      global: ['pres', 'abvs', 'blws', 'psts', 'haln', 'dist', 'abvm', 'blwm', 'calt', 'clig']
    });

    // Setup the indic config for the selected script
    plan.unicodeScript = fromOpenType(plan.script);
    plan.indicConfig = INDIC_CONFIGS[plan.unicodeScript] || INDIC_CONFIGS.Default;
    plan.isOldSpec = plan.indicConfig.hasOldSpec && plan.script[plan.script.length - 1] !== '2';

    // TODO: turn off kern (Khmer) and liga features.
  };

  IndicShaper.assignFeatures = function assignFeatures(plan, glyphs) {
    var _loop = function _loop(i) {
      var codepoint = glyphs[i].codePoints[0];
      var d = INDIC_DECOMPOSITIONS[codepoint] || decompositions[codepoint];
      if (d) {
        var decomposed = d.map(function (c) {
          var g = plan.font.glyphForCodePoint(c);
          return new GlyphInfo(plan.font, g.id, [c], glyphs[i].features);
        });

        glyphs.splice.apply(glyphs, [i, 1].concat(decomposed));
      }
    };

    // Decompose split matras
    // TODO: do this in a more general unicode normalizer
    for (var i = glyphs.length - 1; i >= 0; i--) {
      _loop(i);
    }
  };

  return IndicShaper;
}(DefaultShaper), _class$6.zeroMarkWidths = 'NONE', _temp$2);
function indicCategory(glyph) {
  return trie$1.get(glyph.codePoints[0]) >> 8;
}

function indicPosition(glyph) {
  return 1 << (trie$1.get(glyph.codePoints[0]) & 0xff);
}

var IndicInfo = function IndicInfo(category, position, syllableType, syllable) {
  _classCallCheck(this, IndicInfo);

  this.category = category;
  this.position = position;
  this.syllableType = syllableType;
  this.syllable = syllable;
};

function setupSyllables(font, glyphs) {
  var syllable = 0;
  var last = 0;
  for (var _iterator = stateMachine.match(glyphs.map(indicCategory)), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var _ref2 = _ref,
        start = _ref2[0],
        end = _ref2[1],
        tags = _ref2[2];

    if (start > last) {
      ++syllable;
      for (var _i2 = last; _i2 < start; _i2++) {
        glyphs[_i2].shaperInfo = new IndicInfo(CATEGORIES.X, POSITIONS.End, 'non_indic_cluster', syllable);
      }
    }

    ++syllable;

    // Create shaper info
    for (var _i3 = start; _i3 <= end; _i3++) {
      glyphs[_i3].shaperInfo = new IndicInfo(1 << indicCategory(glyphs[_i3]), indicPosition(glyphs[_i3]), tags[0], syllable);
    }

    last = end + 1;
  }

  if (last < glyphs.length) {
    ++syllable;
    for (var i = last; i < glyphs.length; i++) {
      glyphs[i].shaperInfo = new IndicInfo(CATEGORIES.X, POSITIONS.End, 'non_indic_cluster', syllable);
    }
  }
}

function isConsonant(glyph) {
  return glyph.shaperInfo.category & CONSONANT_FLAGS;
}

function isJoiner(glyph) {
  return glyph.shaperInfo.category & JOINER_FLAGS;
}

function isHalantOrCoeng(glyph) {
  return glyph.shaperInfo.category & HALANT_OR_COENG_FLAGS;
}

function wouldSubstitute(glyphs, feature) {
  for (var _iterator2 = glyphs, _isArray2 = Array.isArray(_iterator2), _i4 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
    var _glyph$features;

    var _ref3;

    if (_isArray2) {
      if (_i4 >= _iterator2.length) break;
      _ref3 = _iterator2[_i4++];
    } else {
      _i4 = _iterator2.next();
      if (_i4.done) break;
      _ref3 = _i4.value;
    }

    var glyph = _ref3;

    glyph.features = (_glyph$features = {}, _glyph$features[feature] = true, _glyph$features);
  }

  var GSUB = glyphs[0]._font._layoutEngine.engine.GSUBProcessor;
  GSUB.applyFeatures([feature], glyphs);

  return glyphs.length === 1;
}

function consonantPosition(font, consonant, virama) {
  var glyphs = [virama, consonant, virama];
  if (wouldSubstitute(glyphs.slice(0, 2), 'blwf') || wouldSubstitute(glyphs.slice(1, 3), 'blwf')) {
    return POSITIONS.Below_C;
  } else if (wouldSubstitute(glyphs.slice(0, 2), 'pstf') || wouldSubstitute(glyphs.slice(1, 3), 'pstf')) {
    return POSITIONS.Post_C;
  } else if (wouldSubstitute(glyphs.slice(0, 2), 'pref') || wouldSubstitute(glyphs.slice(1, 3), 'pref')) {
    return POSITIONS.Post_C;
  }

  return POSITIONS.Base_C;
}

function initialReordering(font, glyphs, plan) {
  var indicConfig = plan.indicConfig;
  var features = font._layoutEngine.engine.GSUBProcessor.features;

  var dottedCircle = font.glyphForCodePoint(0x25cc).id;
  var virama = font.glyphForCodePoint(indicConfig.virama).id;
  if (virama) {
    var info = new GlyphInfo(font, virama, [indicConfig.virama]);
    for (var i = 0; i < glyphs.length; i++) {
      if (glyphs[i].shaperInfo.position === POSITIONS.Base_C) {
        glyphs[i].shaperInfo.position = consonantPosition(font, glyphs[i].copy(), info);
      }
    }
  }

  for (var start = 0, end = nextSyllable(glyphs, 0); start < glyphs.length; start = end, end = nextSyllable(glyphs, start)) {
    var _glyphs$start$shaperI = glyphs[start].shaperInfo,
        category = _glyphs$start$shaperI.category,
        syllableType = _glyphs$start$shaperI.syllableType;


    if (syllableType === 'symbol_cluster' || syllableType === 'non_indic_cluster') {
      continue;
    }

    if (syllableType === 'broken_cluster' && dottedCircle) {
      var g = new GlyphInfo(font, dottedCircle, [0x25cc]);
      g.shaperInfo = new IndicInfo(1 << indicCategory(g), indicPosition(g), glyphs[start].shaperInfo.syllableType, glyphs[start].shaperInfo.syllable);

      // Insert after possible Repha.
      var _i5 = start;
      while (_i5 < end && glyphs[_i5].shaperInfo.category === CATEGORIES.Repha) {
        _i5++;
      }

      glyphs.splice(_i5++, 0, g);
      end++;
    }

    // 1. Find base consonant:
    //
    // The shaping engine finds the base consonant of the syllable, using the
    // following algorithm: starting from the end of the syllable, move backwards
    // until a consonant is found that does not have a below-base or post-base
    // form (post-base forms have to follow below-base forms), or that is not a
    // pre-base reordering Ra, or arrive at the first consonant. The consonant
    // stopped at will be the base.

    var base = end;
    var limit = start;
    var hasReph = false;

    // If the syllable starts with Ra + Halant (in a script that has Reph)
    // and has more than one consonant, Ra is excluded from candidates for
    // base consonants.
    if (indicConfig.rephPos !== POSITIONS.Ra_To_Become_Reph && features.rphf && start + 3 <= end && (indicConfig.rephMode === 'Implicit' && !isJoiner(glyphs[start + 2]) || indicConfig.rephMode === 'Explicit' && glyphs[start + 2].shaperInfo.category === CATEGORIES.ZWJ)) {
      // See if it matches the 'rphf' feature.
      var _g = [glyphs[start].copy(), glyphs[start + 1].copy(), glyphs[start + 2].copy()];
      if (wouldSubstitute(_g.slice(0, 2), 'rphf') || indicConfig.rephMode === 'Explicit' && wouldSubstitute(_g, 'rphf')) {
        limit += 2;
        while (limit < end && isJoiner(glyphs[limit])) {
          limit++;
        }
        base = start;
        hasReph = true;
      }
    } else if (indicConfig.rephMode === 'Log_Repha' && glyphs[start].shaperInfo.category === CATEGORIES.Repha) {
      limit++;
      while (limit < end && isJoiner(glyphs[limit])) {
        limit++;
      }
      base = start;
      hasReph = true;
    }

    switch (indicConfig.basePos) {
      case 'Last':
        {
          // starting from the end of the syllable, move backwards
          var _i6 = end;
          var seenBelow = false;

          do {
            var _info = glyphs[--_i6].shaperInfo;

            // until a consonant is found
            if (isConsonant(glyphs[_i6])) {
              // that does not have a below-base or post-base form
              // (post-base forms have to follow below-base forms),
              if (_info.position !== POSITIONS.Below_C && (_info.position !== POSITIONS.Post_C || seenBelow)) {
                base = _i6;
                break;
              }

              // or that is not a pre-base reordering Ra,
              //
              // IMPLEMENTATION NOTES:
              //
              // Our pre-base reordering Ra's are marked POS_POST_C, so will be skipped
              // by the logic above already.
              //

              // or arrive at the first consonant. The consonant stopped at will
              // be the base.
              if (_info.position === POSITIONS.Below_C) {
                seenBelow = true;
              }

              base = _i6;
            } else if (start < _i6 && _info.category === CATEGORIES.ZWJ && glyphs[_i6 - 1].shaperInfo.category === CATEGORIES.H) {
              // A ZWJ after a Halant stops the base search, and requests an explicit
              // half form.
              // A ZWJ before a Halant, requests a subjoined form instead, and hence
              // search continues.  This is particularly important for Bengali
              // sequence Ra,H,Ya that should form Ya-Phalaa by subjoining Ya.
              break;
            }
          } while (_i6 > limit);
          break;
        }

      case 'First':
        {
          // The first consonant is always the base.
          base = start;

          // Mark all subsequent consonants as below.
          for (var _i7 = base + 1; _i7 < end; _i7++) {
            if (isConsonant(glyphs[_i7])) {
              glyphs[_i7].shaperInfo.position = POSITIONS.Below_C;
            }
          }
        }
    }

    // If the syllable starts with Ra + Halant (in a script that has Reph)
    // and has more than one consonant, Ra is excluded from candidates for
    // base consonants.
    //
    //  Only do this for unforced Reph. (ie. not for Ra,H,ZWJ)
    if (hasReph && base === start && limit - base <= 2) {
      hasReph = false;
    }

    // 2. Decompose and reorder Matras:
    //
    // Each matra and any syllable modifier sign in the cluster are moved to the
    // appropriate position relative to the consonant(s) in the cluster. The
    // shaping engine decomposes two- or three-part matras into their constituent
    // parts before any repositioning. Matra characters are classified by which
    // consonant in a conjunct they have affinity for and are reordered to the
    // following positions:
    //
    //   o Before first half form in the syllable
    //   o After subjoined consonants
    //   o After post-form consonant
    //   o After main consonant (for above marks)
    //
    // IMPLEMENTATION NOTES:
    //
    // The normalize() routine has already decomposed matras for us, so we don't
    // need to worry about that.

    // 3.  Reorder marks to canonical order:
    //
    // Adjacent nukta and halant or nukta and vedic sign are always repositioned
    // if necessary, so that the nukta is first.
    //
    // IMPLEMENTATION NOTES:
    //
    // We don't need to do this: the normalize() routine already did this for us.

    // Reorder characters

    for (var _i8 = start; _i8 < base; _i8++) {
      var _info2 = glyphs[_i8].shaperInfo;
      _info2.position = Math.min(POSITIONS.Pre_C, _info2.position);
    }

    if (base < end) {
      glyphs[base].shaperInfo.position = POSITIONS.Base_C;
    }

    // Mark final consonants.  A final consonant is one appearing after a matra,
    // like in Khmer.
    for (var _i9 = base + 1; _i9 < end; _i9++) {
      if (glyphs[_i9].shaperInfo.category === CATEGORIES.M) {
        for (var j = _i9 + 1; j < end; j++) {
          if (isConsonant(glyphs[j])) {
            glyphs[j].shaperInfo.position = POSITIONS.Final_C;
            break;
          }
        }
        break;
      }
    }

    // Handle beginning Ra
    if (hasReph) {
      glyphs[start].shaperInfo.position = POSITIONS.Ra_To_Become_Reph;
    }

    // For old-style Indic script tags, move the first post-base Halant after
    // last consonant.
    //
    // Reports suggest that in some scripts Uniscribe does this only if there
    // is *not* a Halant after last consonant already (eg. Kannada), while it
    // does it unconditionally in other scripts (eg. Malayalam).  We don't
    // currently know about other scripts, so we single out Malayalam for now.
    //
    // Kannada test case:
    // U+0C9A,U+0CCD,U+0C9A,U+0CCD
    // With some versions of Lohit Kannada.
    // https://bugs.freedesktop.org/show_bug.cgi?id=59118
    //
    // Malayalam test case:
    // U+0D38,U+0D4D,U+0D31,U+0D4D,U+0D31,U+0D4D
    // With lohit-ttf-20121122/Lohit-Malayalam.ttf
    if (plan.isOldSpec) {
      var disallowDoubleHalants = plan.unicodeScript !== 'Malayalam';
      for (var _i10 = base + 1; _i10 < end; _i10++) {
        if (glyphs[_i10].shaperInfo.category === CATEGORIES.H) {
          var _j = void 0;
          for (_j = end - 1; _j > _i10; _j--) {
            if (isConsonant(glyphs[_j]) || disallowDoubleHalants && glyphs[_j].shaperInfo.category === CATEGORIES.H) {
              break;
            }
          }

          if (glyphs[_j].shaperInfo.category !== CATEGORIES.H && _j > _i10) {
            // Move Halant to after last consonant.
            var t = glyphs[_i10];
            glyphs.splice.apply(glyphs, [_i10, 0].concat(glyphs.splice(_i10 + 1, _j - _i10)));
            glyphs[_j] = t;
          }

          break;
        }
      }
    }

    // Attach misc marks to previous char to move with them.
    var lastPos = POSITIONS.Start;
    for (var _i11 = start; _i11 < end; _i11++) {
      var _info3 = glyphs[_i11].shaperInfo;
      if (_info3.category & (JOINER_FLAGS | CATEGORIES.N | CATEGORIES.RS | CATEGORIES.CM | HALANT_OR_COENG_FLAGS & _info3.category)) {
        _info3.position = lastPos;
        if (_info3.category === CATEGORIES.H && _info3.position === POSITIONS.Pre_M) {
          // Uniscribe doesn't move the Halant with Left Matra.
          // TEST: U+092B,U+093F,U+094DE
          // We follow.  This is important for the Sinhala
          // U+0DDA split matra since it decomposes to U+0DD9,U+0DCA
          // where U+0DD9 is a left matra and U+0DCA is the virama.
          // We don't want to move the virama with the left matra.
          // TEST: U+0D9A,U+0DDA
          for (var _j2 = _i11; _j2 > start; _j2--) {
            if (glyphs[_j2 - 1].shaperInfo.position !== POSITIONS.Pre_M) {
              _info3.position = glyphs[_j2 - 1].shaperInfo.position;
              break;
            }
          }
        }
      } else if (_info3.position !== POSITIONS.SMVD) {
        lastPos = _info3.position;
      }
    }

    // For post-base consonants let them own anything before them
    // since the last consonant or matra.
    var last = base;
    for (var _i12 = base + 1; _i12 < end; _i12++) {
      if (isConsonant(glyphs[_i12])) {
        for (var _j3 = last + 1; _j3 < _i12; _j3++) {
          if (glyphs[_j3].shaperInfo.position < POSITIONS.SMVD) {
            glyphs[_j3].shaperInfo.position = glyphs[_i12].shaperInfo.position;
          }
        }
        last = _i12;
      } else if (glyphs[_i12].shaperInfo.category === CATEGORIES.M) {
        last = _i12;
      }
    }

    var arr = glyphs.slice(start, end);
    arr.sort(function (a, b) {
      return a.shaperInfo.position - b.shaperInfo.position;
    });
    glyphs.splice.apply(glyphs, [start, arr.length].concat(arr));

    // Find base again
    for (var _i13 = start; _i13 < end; _i13++) {
      if (glyphs[_i13].shaperInfo.position === POSITIONS.Base_C) {
        base = _i13;
        break;
      }
    }

    // Setup features now

    // Reph
    for (var _i14 = start; _i14 < end && glyphs[_i14].shaperInfo.position === POSITIONS.Ra_To_Become_Reph; _i14++) {
      glyphs[_i14].features.rphf = true;
    }

    // Pre-base
    var blwf = !plan.isOldSpec && indicConfig.blwfMode === 'Pre_And_Post';
    for (var _i15 = start; _i15 < base; _i15++) {
      glyphs[_i15].features.half = true;
      if (blwf) {
        glyphs[_i15].features.blwf = true;
      }
    }

    // Post-base
    for (var _i16 = base + 1; _i16 < end; _i16++) {
      glyphs[_i16].features.abvf = true;
      glyphs[_i16].features.pstf = true;
      glyphs[_i16].features.blwf = true;
    }

    if (plan.isOldSpec && plan.unicodeScript === 'Devanagari') {
      // Old-spec eye-lash Ra needs special handling.  From the
      // spec:
      //
      // "The feature 'below-base form' is applied to consonants
      // having below-base forms and following the base consonant.
      // The exception is vattu, which may appear below half forms
      // as well as below the base glyph. The feature 'below-base
      // form' will be applied to all such occurrences of Ra as well."
      //
      // Test case: U+0924,U+094D,U+0930,U+094d,U+0915
      // with Sanskrit 2003 font.
      //
      // However, note that Ra,Halant,ZWJ is the correct way to
      // request eyelash form of Ra, so we wouldbn't inhibit it
      // in that sequence.
      //
      // Test case: U+0924,U+094D,U+0930,U+094d,U+200D,U+0915
      for (var _i17 = start; _i17 + 1 < base; _i17++) {
        if (glyphs[_i17].shaperInfo.category === CATEGORIES.Ra && glyphs[_i17 + 1].shaperInfo.category === CATEGORIES.H && (_i17 + 1 === base || glyphs[_i17 + 2].shaperInfo.category === CATEGORIES.ZWJ)) {
          glyphs[_i17].features.blwf = true;
          glyphs[_i17 + 1].features.blwf = true;
        }
      }
    }

    var prefLen = 2;
    if (features.pref && base + prefLen < end) {
      // Find a Halant,Ra sequence and mark it for pre-base reordering processing.
      for (var _i18 = base + 1; _i18 + prefLen - 1 < end; _i18++) {
        var _g2 = [glyphs[_i18].copy(), glyphs[_i18 + 1].copy()];
        if (wouldSubstitute(_g2, 'pref')) {
          for (var _j4 = 0; _j4 < prefLen; _j4++) {
            glyphs[_i18++].features.pref = true;
          }

          // Mark the subsequent stuff with 'cfar'.  Used in Khmer.
          // Read the feature spec.
          // This allows distinguishing the following cases with MS Khmer fonts:
          // U+1784,U+17D2,U+179A,U+17D2,U+1782
          // U+1784,U+17D2,U+1782,U+17D2,U+179A
          if (features.cfar) {
            for (; _i18 < end; _i18++) {
              glyphs[_i18].features.cfar = true;
            }
          }

          break;
        }
      }
    }

    // Apply ZWJ/ZWNJ effects
    for (var _i19 = start + 1; _i19 < end; _i19++) {
      if (isJoiner(glyphs[_i19])) {
        var nonJoiner = glyphs[_i19].shaperInfo.category === CATEGORIES.ZWNJ;
        var _j5 = _i19;

        do {
          _j5--;

          // ZWJ/ZWNJ should disable CJCT.  They do that by simply
          // being there, since we don't skip them for the CJCT
          // feature (ie. F_MANUAL_ZWJ)

          // A ZWNJ disables HALF.
          if (nonJoiner) {
            delete glyphs[_j5].features.half;
          }
        } while (_j5 > start && !isConsonant(glyphs[_j5]));
      }
    }
  }
}

function finalReordering(font, glyphs, plan) {
  var indicConfig = plan.indicConfig;
  var features = font._layoutEngine.engine.GSUBProcessor.features;

  for (var start = 0, end = nextSyllable(glyphs, 0); start < glyphs.length; start = end, end = nextSyllable(glyphs, start)) {
    // 4. Final reordering:
    //
    // After the localized forms and basic shaping forms GSUB features have been
    // applied (see below), the shaping engine performs some final glyph
    // reordering before applying all the remaining font features to the entire
    // cluster.

    var tryPref = !!features.pref;

    // Find base again
    var base = start;
    for (; base < end; base++) {
      if (glyphs[base].shaperInfo.position >= POSITIONS.Base_C) {
        if (tryPref && base + 1 < end) {
          for (var i = base + 1; i < end; i++) {
            if (glyphs[i].features.pref) {
              if (!(glyphs[i].substituted && glyphs[i].isLigated && !glyphs[i].isMultiplied)) {
                // Ok, this was a 'pref' candidate but didn't form any.
                // Base is around here...
                base = i;
                while (base < end && isHalantOrCoeng(glyphs[base])) {
                  base++;
                }
                glyphs[base].shaperInfo.position = POSITIONS.BASE_C;
                tryPref = false;
              }
              break;
            }
          }
        }

        // For Malayalam, skip over unformed below- (but NOT post-) forms.
        if (plan.unicodeScript === 'Malayalam') {
          for (var _i20 = base + 1; _i20 < end; _i20++) {
            while (_i20 < end && isJoiner(glyphs[_i20])) {
              _i20++;
            }

            if (_i20 === end || !isHalantOrCoeng(glyphs[_i20])) {
              break;
            }

            _i20++; // Skip halant.
            while (_i20 < end && isJoiner(glyphs[_i20])) {
              _i20++;
            }

            if (_i20 < end && isConsonant(glyphs[_i20]) && glyphs[_i20].shaperInfo.position === POSITIONS.Below_C) {
              base = _i20;
              glyphs[base].shaperInfo.position = POSITIONS.Base_C;
            }
          }
        }

        if (start < base && glyphs[base].shaperInfo.position > POSITIONS.Base_C) {
          base--;
        }
        break;
      }
    }

    if (base === end && start < base && glyphs[base - 1].shaperInfo.category === CATEGORIES.ZWJ) {
      base--;
    }

    if (base < end) {
      while (start < base && glyphs[base].shaperInfo.category & (CATEGORIES.N | HALANT_OR_COENG_FLAGS)) {
        base--;
      }
    }

    // o Reorder matras:
    //
    // If a pre-base matra character had been reordered before applying basic
    // features, the glyph can be moved closer to the main consonant based on
    // whether half-forms had been formed. Actual position for the matra is
    // defined as “after last standalone halant glyph, after initial matra
    // position and before the main consonant”. If ZWJ or ZWNJ follow this
    // halant, position is moved after it.
    //

    if (start + 1 < end && start < base) {
      // Otherwise there can't be any pre-base matra characters.
      // If we lost track of base, alas, position before last thingy.
      var newPos = base === end ? base - 2 : base - 1;

      // Malayalam / Tamil do not have "half" forms or explicit virama forms.
      // The glyphs formed by 'half' are Chillus or ligated explicit viramas.
      // We want to position matra after them.
      if (plan.unicodeScript !== 'Malayalam' && plan.unicodeScript !== 'Tamil') {
        while (newPos > start && !(glyphs[newPos].shaperInfo.category & (CATEGORIES.M | HALANT_OR_COENG_FLAGS))) {
          newPos--;
        }

        // If we found no Halant we are done.
        // Otherwise only proceed if the Halant does
        // not belong to the Matra itself!
        if (isHalantOrCoeng(glyphs[newPos]) && glyphs[newPos].shaperInfo.position !== POSITIONS.Pre_M) {
          // If ZWJ or ZWNJ follow this halant, position is moved after it.
          if (newPos + 1 < end && isJoiner(glyphs[newPos + 1])) {
            newPos++;
          }
        } else {
          newPos = start; // No move.
        }
      }

      if (start < newPos && glyphs[newPos].shaperInfo.position !== POSITIONS.Pre_M) {
        // Now go see if there's actually any matras...
        for (var _i21 = newPos; _i21 > start; _i21--) {
          if (glyphs[_i21 - 1].shaperInfo.position === POSITIONS.Pre_M) {
            var oldPos = _i21 - 1;
            if (oldPos < base && base <= newPos) {
              // Shouldn't actually happen.
              base--;
            }

            var tmp = glyphs[oldPos];
            glyphs.splice.apply(glyphs, [oldPos, 0].concat(glyphs.splice(oldPos + 1, newPos - oldPos)));
            glyphs[newPos] = tmp;

            newPos--;
          }
        }
      }
    }

    // o Reorder reph:
    //
    // Reph’s original position is always at the beginning of the syllable,
    // (i.e. it is not reordered at the character reordering stage). However,
    // it will be reordered according to the basic-forms shaping results.
    // Possible positions for reph, depending on the script, are; after main,
    // before post-base consonant forms, and after post-base consonant forms.

    // Two cases:
    //
    // - If repha is encoded as a sequence of characters (Ra,H or Ra,H,ZWJ), then
    //   we should only move it if the sequence ligated to the repha form.
    //
    // - If repha is encoded separately and in the logical position, we should only
    //   move it if it did NOT ligate.  If it ligated, it's probably the font trying
    //   to make it work without the reordering.
    if (start + 1 < end && glyphs[start].shaperInfo.position === POSITIONS.Ra_To_Become_Reph && glyphs[start].shaperInfo.category === CATEGORIES.Repha !== (glyphs[start].isLigated && !glyphs[start].isMultiplied)) {
      var newRephPos = void 0;
      var rephPos = indicConfig.rephPos;
      var found = false;

      // 1. If reph should be positioned after post-base consonant forms,
      //    proceed to step 5.
      if (rephPos !== POSITIONS.After_Post) {
        //  2. If the reph repositioning class is not after post-base: target
        //     position is after the first explicit halant glyph between the
        //     first post-reph consonant and last main consonant. If ZWJ or ZWNJ
        //     are following this halant, position is moved after it. If such
        //     position is found, this is the target position. Otherwise,
        //     proceed to the next step.
        //
        //     Note: in old-implementation fonts, where classifications were
        //     fixed in shaping engine, there was no case where reph position
        //     will be found on this step.
        newRephPos = start + 1;
        while (newRephPos < base && !isHalantOrCoeng(glyphs[newRephPos])) {
          newRephPos++;
        }

        if (newRephPos < base && isHalantOrCoeng(glyphs[newRephPos])) {
          // ->If ZWJ or ZWNJ are following this halant, position is moved after it.
          if (newRephPos + 1 < base && isJoiner(glyphs[newRephPos + 1])) {
            newRephPos++;
          }

          found = true;
        }

        // 3. If reph should be repositioned after the main consonant: find the
        //    first consonant not ligated with main, or find the first
        //    consonant that is not a potential pre-base reordering Ra.
        if (!found && rephPos === POSITIONS.After_Main) {
          newRephPos = base;
          while (newRephPos + 1 < end && glyphs[newRephPos + 1].shaperInfo.position <= POSITIONS.After_Main) {
            newRephPos++;
          }

          found = newRephPos < end;
        }

        // 4. If reph should be positioned before post-base consonant, find
        //    first post-base classified consonant not ligated with main. If no
        //    consonant is found, the target position should be before the
        //    first matra, syllable modifier sign or vedic sign.
        //
        // This is our take on what step 4 is trying to say (and failing, BADLY).
        if (!found && rephPos === POSITIONS.After_Sub) {
          newRephPos = base;
          while (newRephPos + 1 < end && !(glyphs[newRephPos + 1].shaperInfo.position & (POSITIONS.Post_C | POSITIONS.After_Post | POSITIONS.SMVD))) {
            newRephPos++;
          }

          found = newRephPos < end;
        }
      }

      //  5. If no consonant is found in steps 3 or 4, move reph to a position
      //     immediately before the first post-base matra, syllable modifier
      //     sign or vedic sign that has a reordering class after the intended
      //     reph position. For example, if the reordering position for reph
      //     is post-main, it will skip above-base matras that also have a
      //     post-main position.
      if (!found) {
        // Copied from step 2.
        newRephPos = start + 1;
        while (newRephPos < base && !isHalantOrCoeng(glyphs[newRephPos])) {
          newRephPos++;
        }

        if (newRephPos < base && isHalantOrCoeng(glyphs[newRephPos])) {
          // ->If ZWJ or ZWNJ are following this halant, position is moved after it.
          if (newRephPos + 1 < base && isJoiner(glyphs[newRephPos + 1])) {
            newRephPos++;
          }

          found = true;
        }
      }

      // 6. Otherwise, reorder reph to the end of the syllable.
      if (!found) {
        newRephPos = end - 1;
        while (newRephPos > start && glyphs[newRephPos].shaperInfo.position === POSITIONS.SMVD) {
          newRephPos--;
        }

        // If the Reph is to be ending up after a Matra,Halant sequence,
        // position it before that Halant so it can interact with the Matra.
        // However, if it's a plain Consonant,Halant we shouldn't do that.
        // Uniscribe doesn't do this.
        // TEST: U+0930,U+094D,U+0915,U+094B,U+094D
        if (isHalantOrCoeng(glyphs[newRephPos])) {
          for (var _i22 = base + 1; _i22 < newRephPos; _i22++) {
            if (glyphs[_i22].shaperInfo.category === CATEGORIES.M) {
              newRephPos--;
            }
          }
        }
      }

      var reph = glyphs[start];
      glyphs.splice.apply(glyphs, [start, 0].concat(glyphs.splice(start + 1, newRephPos - start)));
      glyphs[newRephPos] = reph;

      if (start < base && base <= newRephPos) {
        base--;
      }
    }

    // o Reorder pre-base reordering consonants:
    //
    // If a pre-base reordering consonant is found, reorder it according to
    // the following rules:
    if (tryPref && base + 1 < end) {
      for (var _i23 = base + 1; _i23 < end; _i23++) {
        if (glyphs[_i23].features.pref) {
          // 1. Only reorder a glyph produced by substitution during application
          //    of the <pref> feature. (Note that a font may shape a Ra consonant with
          //    the feature generally but block it in certain contexts.)

          // Note: We just check that something got substituted.  We don't check that
          // the <pref> feature actually did it...
          //
          // Reorder pref only if it ligated.
          if (glyphs[_i23].isLigated && !glyphs[_i23].isMultiplied) {
            // 2. Try to find a target position the same way as for pre-base matra.
            //    If it is found, reorder pre-base consonant glyph.
            //
            // 3. If position is not found, reorder immediately before main
            //    consonant.
            var _newPos = base;

            // Malayalam / Tamil do not have "half" forms or explicit virama forms.
            // The glyphs formed by 'half' are Chillus or ligated explicit viramas.
            // We want to position matra after them.
            if (plan.unicodeScript !== 'Malayalam' && plan.unicodeScript !== 'Tamil') {
              while (_newPos > start && !(glyphs[_newPos - 1].shaperInfo.category & (CATEGORIES.M | HALANT_OR_COENG_FLAGS))) {
                _newPos--;
              }

              // In Khmer coeng model, a H,Ra can go *after* matras.  If it goes after a
              // split matra, it should be reordered to *before* the left part of such matra.
              if (_newPos > start && glyphs[_newPos - 1].shaperInfo.category === CATEGORIES.M) {
                var _oldPos2 = _i23;
                for (var j = base + 1; j < _oldPos2; j++) {
                  if (glyphs[j].shaperInfo.category === CATEGORIES.M) {
                    _newPos--;
                    break;
                  }
                }
              }
            }

            if (_newPos > start && isHalantOrCoeng(glyphs[_newPos - 1])) {
              // -> If ZWJ or ZWNJ follow this halant, position is moved after it.
              if (_newPos < end && isJoiner(glyphs[_newPos])) {
                _newPos++;
              }
            }

            var _oldPos = _i23;
            var _tmp = glyphs[_oldPos];
            glyphs.splice.apply(glyphs, [_newPos + 1, 0].concat(glyphs.splice(_newPos, _oldPos - _newPos)));
            glyphs[_newPos] = _tmp;

            if (_newPos <= base && base < _oldPos) {
              base++;
            }
          }

          break;
        }
      }
    }

    // Apply 'init' to the Left Matra if it's a word start.
    if (glyphs[start].shaperInfo.position === POSITIONS.Pre_M && (!start || !/Cf|Mn/.test(unicode.getCategory(glyphs[start - 1].codePoints[0])))) {
      glyphs[start].features.init = true;
    }
  }
}

function nextSyllable(glyphs, start) {
  if (start >= glyphs.length) return start;
  var syllable = glyphs[start].shaperInfo.syllable;
  while (++start < glyphs.length && glyphs[start].shaperInfo.syllable === syllable) {}
  return start;
}

var _class$7;
var _temp$3;
var categories$1 = useData.categories;
var decompositions$2 = useData.decompositions;
var trie$2 = new UnicodeTrie(Buffer("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","base64"));
var stateMachine$1 = new StateMachine(useData);

/**
 * This shaper is an implementation of the Universal Shaping Engine, which
 * uses Unicode data to shape a number of scripts without a dedicated shaping engine.
 * See https://www.microsoft.com/typography/OpenTypeDev/USE/intro.htm.
 */
var UniversalShaper = (_temp$3 = _class$7 = function (_DefaultShaper) {
  _inherits(UniversalShaper, _DefaultShaper);

  function UniversalShaper() {
    _classCallCheck(this, UniversalShaper);

    return _possibleConstructorReturn(this, _DefaultShaper.apply(this, arguments));
  }

  UniversalShaper.planFeatures = function planFeatures(plan) {
    plan.addStage(setupSyllables$1);

    // Default glyph pre-processing group
    plan.addStage(['locl', 'ccmp', 'nukt', 'akhn']);

    // Reordering group
    plan.addStage(clearSubstitutionFlags);
    plan.addStage(['rphf'], false);
    plan.addStage(recordRphf);
    plan.addStage(clearSubstitutionFlags);
    plan.addStage(['pref']);
    plan.addStage(recordPref);

    // Orthographic unit shaping group
    plan.addStage(['rkrf', 'abvf', 'blwf', 'half', 'pstf', 'vatu', 'cjct']);
    plan.addStage(reorder);

    // Topographical features
    // Scripts that need this are handled by the Arabic shaper, not implemented here for now.
    // plan.addStage(['isol', 'init', 'medi', 'fina', 'med2', 'fin2', 'fin3'], false);

    // Standard topographic presentation and positional feature application
    plan.addStage(['abvs', 'blws', 'pres', 'psts', 'dist', 'abvm', 'blwm']);
  };

  UniversalShaper.assignFeatures = function assignFeatures(plan, glyphs) {
    var _loop = function _loop(i) {
      var codepoint = glyphs[i].codePoints[0];
      if (decompositions$2[codepoint]) {
        var decomposed = decompositions$2[codepoint].map(function (c) {
          var g = plan.font.glyphForCodePoint(c);
          return new GlyphInfo(plan.font, g.id, [c], glyphs[i].features);
        });

        glyphs.splice.apply(glyphs, [i, 1].concat(decomposed));
      }
    };

    // Decompose split vowels
    // TODO: do this in a more general unicode normalizer
    for (var i = glyphs.length - 1; i >= 0; i--) {
      _loop(i);
    }
  };

  return UniversalShaper;
}(DefaultShaper), _class$7.zeroMarkWidths = 'BEFORE_GPOS', _temp$3);
function useCategory(glyph) {
  return trie$2.get(glyph.codePoints[0]);
}

var USEInfo = function USEInfo(category, syllableType, syllable) {
  _classCallCheck(this, USEInfo);

  this.category = category;
  this.syllableType = syllableType;
  this.syllable = syllable;
};

function setupSyllables$1(font, glyphs) {
  var syllable = 0;
  for (var _iterator = stateMachine$1.match(glyphs.map(useCategory)), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var _ref2 = _ref,
        start = _ref2[0],
        end = _ref2[1],
        tags = _ref2[2];

    ++syllable;

    // Create shaper info
    for (var i = start; i <= end; i++) {
      glyphs[i].shaperInfo = new USEInfo(categories$1[useCategory(glyphs[i])], tags[0], syllable);
    }

    // Assign rphf feature
    var limit = glyphs[start].shaperInfo.category === 'R' ? 1 : Math.min(3, end - start);
    for (var _i2 = start; _i2 < start + limit; _i2++) {
      glyphs[_i2].features.rphf = true;
    }
  }
}

function clearSubstitutionFlags(font, glyphs) {
  for (var _iterator2 = glyphs, _isArray2 = Array.isArray(_iterator2), _i3 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
    var _ref3;

    if (_isArray2) {
      if (_i3 >= _iterator2.length) break;
      _ref3 = _iterator2[_i3++];
    } else {
      _i3 = _iterator2.next();
      if (_i3.done) break;
      _ref3 = _i3.value;
    }

    var glyph = _ref3;

    glyph.substituted = false;
  }
}

function recordRphf(font, glyphs) {
  for (var _iterator3 = glyphs, _isArray3 = Array.isArray(_iterator3), _i4 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
    var _ref4;

    if (_isArray3) {
      if (_i4 >= _iterator3.length) break;
      _ref4 = _iterator3[_i4++];
    } else {
      _i4 = _iterator3.next();
      if (_i4.done) break;
      _ref4 = _i4.value;
    }

    var glyph = _ref4;

    if (glyph.substituted && glyph.features.rphf) {
      // Mark a substituted repha.
      glyph.shaperInfo.category = 'R';
    }
  }
}

function recordPref(font, glyphs) {
  for (var _iterator4 = glyphs, _isArray4 = Array.isArray(_iterator4), _i5 = 0, _iterator4 = _isArray4 ? _iterator4 : _getIterator(_iterator4);;) {
    var _ref5;

    if (_isArray4) {
      if (_i5 >= _iterator4.length) break;
      _ref5 = _iterator4[_i5++];
    } else {
      _i5 = _iterator4.next();
      if (_i5.done) break;
      _ref5 = _i5.value;
    }

    var glyph = _ref5;

    if (glyph.substituted) {
      // Mark a substituted pref as VPre, as they behave the same way.
      glyph.shaperInfo.category = 'VPre';
    }
  }
}

function reorder(font, glyphs) {
  var dottedCircle = font.glyphForCodePoint(0x25cc).id;

  for (var start = 0, end = nextSyllable$1(glyphs, 0); start < glyphs.length; start = end, end = nextSyllable$1(glyphs, start)) {
    var i = void 0,
        j = void 0;
    var info = glyphs[start].shaperInfo;
    var type = info.syllableType;

    // Only a few syllable types need reordering.
    if (type !== 'virama_terminated_cluster' && type !== 'standard_cluster' && type !== 'broken_cluster') {
      continue;
    }

    // Insert a dotted circle glyph in broken clusters.
    if (type === 'broken_cluster' && dottedCircle) {
      var g = new GlyphInfo(font, dottedCircle, [0x25cc]);
      g.shaperInfo = info;

      // Insert after possible Repha.
      for (i = start; i < end && glyphs[i].shaperInfo.category === 'R'; i++) {}
      glyphs.splice(++i, 0, g);
      end++;
    }

    // Move things forward.
    if (info.category === 'R' && end - start > 1) {
      // Got a repha. Reorder it to after first base, before first halant.
      for (i = start + 1; i < end; i++) {
        info = glyphs[i].shaperInfo;
        if (isBase(info) || isHalant(glyphs[i])) {
          // If we hit a halant, move before it; otherwise it's a base: move to it's
          // place, and shift things in between backward.
          if (isHalant(glyphs[i])) {
            i--;
          }

          glyphs.splice.apply(glyphs, [start, 0].concat(glyphs.splice(start + 1, i - start), [glyphs[i]]));
          break;
        }
      }
    }

    // Move things back.
    for (i = start, j = end; i < end; i++) {
      info = glyphs[i].shaperInfo;
      if (isBase(info) || isHalant(glyphs[i])) {
        // If we hit a halant, move after it; otherwise it's a base: move to it's
        // place, and shift things in between backward.
        j = isHalant(glyphs[i]) ? i + 1 : i;
      } else if ((info.category === 'VPre' || info.category === 'VMPre') && j < i) {
        glyphs.splice.apply(glyphs, [j, 1, glyphs[i]].concat(glyphs.splice(j, i - j)));
      }
    }
  }
}

function nextSyllable$1(glyphs, start) {
  if (start >= glyphs.length) return start;
  var syllable = glyphs[start].shaperInfo.syllable;
  while (++start < glyphs.length && glyphs[start].shaperInfo.syllable === syllable) {}
  return start;
}

function isHalant(glyph) {
  return glyph.shaperInfo.category === 'H' && !glyph.isLigated;
}

function isBase(info) {
  return info.category === 'B' || info.category === 'GB';
}

var SHAPERS = {
  arab: ArabicShaper, // Arabic
  mong: ArabicShaper, // Mongolian
  syrc: ArabicShaper, // Syriac
  'nko ': ArabicShaper, // N'Ko
  phag: ArabicShaper, // Phags Pa
  mand: ArabicShaper, // Mandaic
  mani: ArabicShaper, // Manichaean
  phlp: ArabicShaper, // Psalter Pahlavi

  hang: HangulShaper, // Hangul

  bng2: IndicShaper, // Bengali
  beng: IndicShaper, // Bengali
  dev2: IndicShaper, // Devanagari
  deva: IndicShaper, // Devanagari
  gjr2: IndicShaper, // Gujarati
  gujr: IndicShaper, // Gujarati
  guru: IndicShaper, // Gurmukhi
  gur2: IndicShaper, // Gurmukhi
  knda: IndicShaper, // Kannada
  knd2: IndicShaper, // Kannada
  mlm2: IndicShaper, // Malayalam
  mlym: IndicShaper, // Malayalam
  ory2: IndicShaper, // Oriya
  orya: IndicShaper, // Oriya
  taml: IndicShaper, // Tamil
  tml2: IndicShaper, // Tamil
  telu: IndicShaper, // Telugu
  tel2: IndicShaper, // Telugu
  khmr: IndicShaper, // Khmer

  bali: UniversalShaper, // Balinese
  batk: UniversalShaper, // Batak
  brah: UniversalShaper, // Brahmi
  bugi: UniversalShaper, // Buginese
  buhd: UniversalShaper, // Buhid
  cakm: UniversalShaper, // Chakma
  cham: UniversalShaper, // Cham
  dupl: UniversalShaper, // Duployan
  egyp: UniversalShaper, // Egyptian Hieroglyphs
  gran: UniversalShaper, // Grantha
  hano: UniversalShaper, // Hanunoo
  java: UniversalShaper, // Javanese
  kthi: UniversalShaper, // Kaithi
  kali: UniversalShaper, // Kayah Li
  khar: UniversalShaper, // Kharoshthi
  khoj: UniversalShaper, // Khojki
  sind: UniversalShaper, // Khudawadi
  lepc: UniversalShaper, // Lepcha
  limb: UniversalShaper, // Limbu
  mahj: UniversalShaper, // Mahajani
  // mand: UniversalShaper, // Mandaic
  // mani: UniversalShaper, // Manichaean
  mtei: UniversalShaper, // Meitei Mayek
  modi: UniversalShaper, // Modi
  // mong: UniversalShaper, // Mongolian
  // 'nko ': UniversalShaper, // N’Ko
  hmng: UniversalShaper, // Pahawh Hmong
  // phag: UniversalShaper, // Phags-pa
  // phlp: UniversalShaper, // Psalter Pahlavi
  rjng: UniversalShaper, // Rejang
  saur: UniversalShaper, // Saurashtra
  shrd: UniversalShaper, // Sharada
  sidd: UniversalShaper, // Siddham
  sinh: UniversalShaper, // Sinhala
  sund: UniversalShaper, // Sundanese
  sylo: UniversalShaper, // Syloti Nagri
  tglg: UniversalShaper, // Tagalog
  tagb: UniversalShaper, // Tagbanwa
  tale: UniversalShaper, // Tai Le
  lana: UniversalShaper, // Tai Tham
  tavt: UniversalShaper, // Tai Viet
  takr: UniversalShaper, // Takri
  tibt: UniversalShaper, // Tibetan
  tfng: UniversalShaper, // Tifinagh
  tirh: UniversalShaper, // Tirhuta

  latn: DefaultShaper, // Latin
  DFLT: DefaultShaper // Default
};

function choose(script) {
  if (!Array.isArray(script)) {
    script = [script];
  }

  for (var _iterator = script, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var s = _ref;

    var shaper = SHAPERS[s];
    if (shaper) {
      return shaper;
    }
  }

  return DefaultShaper;
}

var GSUBProcessor = function (_OTProcessor) {
  _inherits(GSUBProcessor, _OTProcessor);

  function GSUBProcessor() {
    _classCallCheck(this, GSUBProcessor);

    return _possibleConstructorReturn(this, _OTProcessor.apply(this, arguments));
  }

  GSUBProcessor.prototype.applyLookup = function applyLookup(lookupType, table) {
    var _this2 = this;

    switch (lookupType) {
      case 1:
        {
          // Single Substitution
          var index = this.coverageIndex(table.coverage);
          if (index === -1) {
            return false;
          }

          var glyph = this.glyphIterator.cur;
          switch (table.version) {
            case 1:
              glyph.id = glyph.id + table.deltaGlyphID & 0xffff;
              break;

            case 2:
              glyph.id = table.substitute.get(index);
              break;
          }

          return true;
        }

      case 2:
        {
          // Multiple Substitution
          var _index = this.coverageIndex(table.coverage);
          if (_index !== -1) {
            var _glyphs;

            var sequence = table.sequences.get(_index);

            if (sequence.length === 0) {
              // If the sequence length is zero, delete the glyph.
              // The OpenType spec disallows this, but seems like Harfbuzz and Uniscribe allow it.
              this.glyphs.splice(this.glyphIterator.index, 1);
              return true;
            }

            this.glyphIterator.cur.id = sequence[0];
            this.glyphIterator.cur.ligatureComponent = 0;

            var features = this.glyphIterator.cur.features;
            var curGlyph = this.glyphIterator.cur;
            var replacement = sequence.slice(1).map(function (gid, i) {
              var glyph = new GlyphInfo(_this2.font, gid, undefined, features);
              glyph.shaperInfo = curGlyph.shaperInfo;
              glyph.isLigated = curGlyph.isLigated;
              glyph.ligatureComponent = i + 1;
              glyph.substituted = true;
              glyph.isMultiplied = true;
              return glyph;
            });

            (_glyphs = this.glyphs).splice.apply(_glyphs, [this.glyphIterator.index + 1, 0].concat(replacement));
            return true;
          }

          return false;
        }

      case 3:
        {
          // Alternate Substitution
          var _index2 = this.coverageIndex(table.coverage);
          if (_index2 !== -1) {
            var USER_INDEX = 0; // TODO
            this.glyphIterator.cur.id = table.alternateSet.get(_index2)[USER_INDEX];
            return true;
          }

          return false;
        }

      case 4:
        {
          // Ligature Substitution
          var _index3 = this.coverageIndex(table.coverage);
          if (_index3 === -1) {
            return false;
          }

          for (var _iterator = table.ligatureSets.get(_index3), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
            var _ref;

            if (_isArray) {
              if (_i >= _iterator.length) break;
              _ref = _iterator[_i++];
            } else {
              _i = _iterator.next();
              if (_i.done) break;
              _ref = _i.value;
            }

            var ligature = _ref;

            var matched = this.sequenceMatchIndices(1, ligature.components);
            if (!matched) {
              continue;
            }

            var _curGlyph = this.glyphIterator.cur;

            // Concatenate all of the characters the new ligature will represent
            var characters = _curGlyph.codePoints.slice();
            for (var _iterator2 = matched, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
              var _ref2;

              if (_isArray2) {
                if (_i2 >= _iterator2.length) break;
                _ref2 = _iterator2[_i2++];
              } else {
                _i2 = _iterator2.next();
                if (_i2.done) break;
                _ref2 = _i2.value;
              }

              var _index4 = _ref2;

              characters.push.apply(characters, this.glyphs[_index4].codePoints);
            }

            // Create the replacement ligature glyph
            var ligatureGlyph = new GlyphInfo(this.font, ligature.glyph, characters, _curGlyph.features);
            ligatureGlyph.shaperInfo = _curGlyph.shaperInfo;
            ligatureGlyph.isLigated = true;
            ligatureGlyph.substituted = true;

            // From Harfbuzz:
            // - If it *is* a mark ligature, we don't allocate a new ligature id, and leave
            //   the ligature to keep its old ligature id.  This will allow it to attach to
            //   a base ligature in GPOS.  Eg. if the sequence is: LAM,LAM,SHADDA,FATHA,HEH,
            //   and LAM,LAM,HEH for a ligature, they will leave SHADDA and FATHA with a
            //   ligature id and component value of 2.  Then if SHADDA,FATHA form a ligature
            //   later, we don't want them to lose their ligature id/component, otherwise
            //   GPOS will fail to correctly position the mark ligature on top of the
            //   LAM,LAM,HEH ligature. See https://bugzilla.gnome.org/show_bug.cgi?id=676343
            //
            // - If a ligature is formed of components that some of which are also ligatures
            //   themselves, and those ligature components had marks attached to *their*
            //   components, we have to attach the marks to the new ligature component
            //   positions!  Now *that*'s tricky!  And these marks may be following the
            //   last component of the whole sequence, so we should loop forward looking
            //   for them and update them.
            //
            //   Eg. the sequence is LAM,LAM,SHADDA,FATHA,HEH, and the font first forms a
            //   'calt' ligature of LAM,HEH, leaving the SHADDA and FATHA with a ligature
            //   id and component == 1.  Now, during 'liga', the LAM and the LAM-HEH ligature
            //   form a LAM-LAM-HEH ligature.  We need to reassign the SHADDA and FATHA to
            //   the new ligature with a component value of 2.
            //
            //   This in fact happened to a font...  See https://bugzilla.gnome.org/show_bug.cgi?id=437633
            var isMarkLigature = _curGlyph.isMark;
            for (var i = 0; i < matched.length && isMarkLigature; i++) {
              isMarkLigature = this.glyphs[matched[i]].isMark;
            }

            ligatureGlyph.ligatureID = isMarkLigature ? null : this.ligatureID++;

            var lastLigID = _curGlyph.ligatureID;
            var lastNumComps = _curGlyph.codePoints.length;
            var curComps = lastNumComps;
            var idx = this.glyphIterator.index + 1;

            // Set ligatureID and ligatureComponent on glyphs that were skipped in the matched sequence.
            // This allows GPOS to attach marks to the correct ligature components.
            for (var _iterator3 = matched, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
              var _ref3;

              if (_isArray3) {
                if (_i3 >= _iterator3.length) break;
                _ref3 = _iterator3[_i3++];
              } else {
                _i3 = _iterator3.next();
                if (_i3.done) break;
                _ref3 = _i3.value;
              }

              var matchIndex = _ref3;

              // Don't assign new ligature components for mark ligatures (see above)
              if (isMarkLigature) {
                idx = matchIndex;
              } else {
                while (idx < matchIndex) {
                  var ligatureComponent = curComps - lastNumComps + Math.min(this.glyphs[idx].ligatureComponent || 1, lastNumComps);
                  this.glyphs[idx].ligatureID = ligatureGlyph.ligatureID;
                  this.glyphs[idx].ligatureComponent = ligatureComponent;
                  idx++;
                }
              }

              lastLigID = this.glyphs[idx].ligatureID;
              lastNumComps = this.glyphs[idx].codePoints.length;
              curComps += lastNumComps;
              idx++; // skip base glyph
            }

            // Adjust ligature components for any marks following
            if (lastLigID && !isMarkLigature) {
              for (var _i4 = idx; _i4 < this.glyphs.length; _i4++) {
                if (this.glyphs[_i4].ligatureID === lastLigID) {
                  var ligatureComponent = curComps - lastNumComps + Math.min(this.glyphs[_i4].ligatureComponent || 1, lastNumComps);
                  this.glyphs[_i4].ligatureComponent = ligatureComponent;
                } else {
                  break;
                }
              }
            }

            // Delete the matched glyphs, and replace the current glyph with the ligature glyph
            for (var _i5 = matched.length - 1; _i5 >= 0; _i5--) {
              this.glyphs.splice(matched[_i5], 1);
            }

            this.glyphs[this.glyphIterator.index] = ligatureGlyph;
            return true;
          }

          return false;
        }

      case 5:
        // Contextual Substitution
        return this.applyContext(table);

      case 6:
        // Chaining Contextual Substitution
        return this.applyChainingContext(table);

      case 7:
        // Extension Substitution
        return this.applyLookup(table.lookupType, table.extension);

      default:
        throw new Error('GSUB lookupType ' + lookupType + ' is not supported');
    }
  };

  return GSUBProcessor;
}(OTProcessor);

var GPOSProcessor = function (_OTProcessor) {
  _inherits(GPOSProcessor, _OTProcessor);

  function GPOSProcessor() {
    _classCallCheck(this, GPOSProcessor);

    return _possibleConstructorReturn(this, _OTProcessor.apply(this, arguments));
  }

  GPOSProcessor.prototype.applyPositionValue = function applyPositionValue(sequenceIndex, value) {
    var position = this.positions[this.glyphIterator.peekIndex(sequenceIndex)];
    if (value.xAdvance != null) {
      position.xAdvance += value.xAdvance;
    }

    if (value.yAdvance != null) {
      position.yAdvance += value.yAdvance;
    }

    if (value.xPlacement != null) {
      position.xOffset += value.xPlacement;
    }

    if (value.yPlacement != null) {
      position.yOffset += value.yPlacement;
    }

    // Adjustments for font variations
    var variationProcessor = this.font._variationProcessor;
    var variationStore = this.font.GDEF && this.font.GDEF.itemVariationStore;
    if (variationProcessor && variationStore) {
      if (value.xPlaDevice) {
        position.xOffset += variationProcessor.getDelta(variationStore, value.xPlaDevice.a, value.xPlaDevice.b);
      }

      if (value.yPlaDevice) {
        position.yOffset += variationProcessor.getDelta(variationStore, value.yPlaDevice.a, value.yPlaDevice.b);
      }

      if (value.xAdvDevice) {
        position.xAdvance += variationProcessor.getDelta(variationStore, value.xAdvDevice.a, value.xAdvDevice.b);
      }

      if (value.yAdvDevice) {
        position.yAdvance += variationProcessor.getDelta(variationStore, value.yAdvDevice.a, value.yAdvDevice.b);
      }
    }

    // TODO: device tables
  };

  GPOSProcessor.prototype.applyLookup = function applyLookup(lookupType, table) {
    switch (lookupType) {
      case 1:
        {
          // Single positioning value
          var index = this.coverageIndex(table.coverage);
          if (index === -1) {
            return false;
          }

          switch (table.version) {
            case 1:
              this.applyPositionValue(0, table.value);
              break;

            case 2:
              this.applyPositionValue(0, table.values.get(index));
              break;
          }

          return true;
        }

      case 2:
        {
          // Pair Adjustment Positioning
          var nextGlyph = this.glyphIterator.peek();
          if (!nextGlyph) {
            return false;
          }

          var _index = this.coverageIndex(table.coverage);
          if (_index === -1) {
            return false;
          }

          switch (table.version) {
            case 1:
              // Adjustments for glyph pairs
              var set = table.pairSets.get(_index);

              for (var _iterator = set, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
                var _ref;

                if (_isArray) {
                  if (_i >= _iterator.length) break;
                  _ref = _iterator[_i++];
                } else {
                  _i = _iterator.next();
                  if (_i.done) break;
                  _ref = _i.value;
                }

                var _pair = _ref;

                if (_pair.secondGlyph === nextGlyph.id) {
                  this.applyPositionValue(0, _pair.value1);
                  this.applyPositionValue(1, _pair.value2);
                  return true;
                }
              }

              return false;

            case 2:
              // Class pair adjustment
              var class1 = this.getClassID(this.glyphIterator.cur.id, table.classDef1);
              var class2 = this.getClassID(nextGlyph.id, table.classDef2);
              if (class1 === -1 || class2 === -1) {
                return false;
              }

              var pair = table.classRecords.get(class1).get(class2);
              this.applyPositionValue(0, pair.value1);
              this.applyPositionValue(1, pair.value2);
              return true;
          }
        }

      case 3:
        {
          // Cursive Attachment Positioning
          var nextIndex = this.glyphIterator.peekIndex();
          var _nextGlyph = this.glyphs[nextIndex];
          if (!_nextGlyph) {
            return false;
          }

          var curRecord = table.entryExitRecords[this.coverageIndex(table.coverage)];
          if (!curRecord || !curRecord.exitAnchor) {
            return false;
          }

          var nextRecord = table.entryExitRecords[this.coverageIndex(table.coverage, _nextGlyph.id)];
          if (!nextRecord || !nextRecord.entryAnchor) {
            return false;
          }

          var entry = this.getAnchor(nextRecord.entryAnchor);
          var exit = this.getAnchor(curRecord.exitAnchor);

          var cur = this.positions[this.glyphIterator.index];
          var next = this.positions[nextIndex];

          switch (this.direction) {
            case 'ltr':
              cur.xAdvance = exit.x + cur.xOffset;

              var d = entry.x + next.xOffset;
              next.xAdvance -= d;
              next.xOffset -= d;
              break;

            case 'rtl':
              d = exit.x + cur.xOffset;
              cur.xAdvance -= d;
              cur.xOffset -= d;
              next.xAdvance = entry.x + next.xOffset;
              break;
          }

          if (this.glyphIterator.flags.rightToLeft) {
            this.glyphIterator.cur.cursiveAttachment = nextIndex;
            cur.yOffset = entry.y - exit.y;
          } else {
            _nextGlyph.cursiveAttachment = this.glyphIterator.index;
            cur.yOffset = exit.y - entry.y;
          }

          return true;
        }

      case 4:
        {
          // Mark to base positioning
          var markIndex = this.coverageIndex(table.markCoverage);
          if (markIndex === -1) {
            return false;
          }

          // search backward for a base glyph
          var baseGlyphIndex = this.glyphIterator.index;
          while (--baseGlyphIndex >= 0 && (this.glyphs[baseGlyphIndex].isMark || this.glyphs[baseGlyphIndex].ligatureComponent > 0)) {}

          if (baseGlyphIndex < 0) {
            return false;
          }

          var baseIndex = this.coverageIndex(table.baseCoverage, this.glyphs[baseGlyphIndex].id);
          if (baseIndex === -1) {
            return false;
          }

          var markRecord = table.markArray[markIndex];
          var baseAnchor = table.baseArray[baseIndex][markRecord.class];
          this.applyAnchor(markRecord, baseAnchor, baseGlyphIndex);
          return true;
        }

      case 5:
        {
          // Mark to ligature positioning
          var _markIndex = this.coverageIndex(table.markCoverage);
          if (_markIndex === -1) {
            return false;
          }

          // search backward for a base glyph
          var _baseGlyphIndex = this.glyphIterator.index;
          while (--_baseGlyphIndex >= 0 && this.glyphs[_baseGlyphIndex].isMark) {}

          if (_baseGlyphIndex < 0) {
            return false;
          }

          var ligIndex = this.coverageIndex(table.ligatureCoverage, this.glyphs[_baseGlyphIndex].id);
          if (ligIndex === -1) {
            return false;
          }

          var ligAttach = table.ligatureArray[ligIndex];
          var markGlyph = this.glyphIterator.cur;
          var ligGlyph = this.glyphs[_baseGlyphIndex];
          var compIndex = ligGlyph.ligatureID && ligGlyph.ligatureID === markGlyph.ligatureID && markGlyph.ligatureComponent > 0 ? Math.min(markGlyph.ligatureComponent, ligGlyph.codePoints.length) - 1 : ligGlyph.codePoints.length - 1;

          var _markRecord = table.markArray[_markIndex];
          var _baseAnchor = ligAttach[compIndex][_markRecord.class];
          this.applyAnchor(_markRecord, _baseAnchor, _baseGlyphIndex);
          return true;
        }

      case 6:
        {
          // Mark to mark positioning
          var mark1Index = this.coverageIndex(table.mark1Coverage);
          if (mark1Index === -1) {
            return false;
          }

          // get the previous mark to attach to
          var prevIndex = this.glyphIterator.peekIndex(-1);
          var prev = this.glyphs[prevIndex];
          if (!prev || !prev.isMark) {
            return false;
          }

          var _cur = this.glyphIterator.cur;

          // The following logic was borrowed from Harfbuzz
          var good = false;
          if (_cur.ligatureID === prev.ligatureID) {
            if (!_cur.ligatureID) {
              // Marks belonging to the same base
              good = true;
            } else if (_cur.ligatureComponent === prev.ligatureComponent) {
              // Marks belonging to the same ligature component
              good = true;
            }
          } else {
            // If ligature ids don't match, it may be the case that one of the marks
            // itself is a ligature, in which case match.
            if (_cur.ligatureID && !_cur.ligatureComponent || prev.ligatureID && !prev.ligatureComponent) {
              good = true;
            }
          }

          if (!good) {
            return false;
          }

          var mark2Index = this.coverageIndex(table.mark2Coverage, prev.id);
          if (mark2Index === -1) {
            return false;
          }

          var _markRecord2 = table.mark1Array[mark1Index];
          var _baseAnchor2 = table.mark2Array[mark2Index][_markRecord2.class];
          this.applyAnchor(_markRecord2, _baseAnchor2, prevIndex);
          return true;
        }

      case 7:
        // Contextual positioning
        return this.applyContext(table);

      case 8:
        // Chaining contextual positioning
        return this.applyChainingContext(table);

      case 9:
        // Extension positioning
        return this.applyLookup(table.lookupType, table.extension);

      default:
        throw new Error('Unsupported GPOS table: ' + lookupType);
    }
  };

  GPOSProcessor.prototype.applyAnchor = function applyAnchor(markRecord, baseAnchor, baseGlyphIndex) {
    var baseCoords = this.getAnchor(baseAnchor);
    var markCoords = this.getAnchor(markRecord.markAnchor);

    var basePos = this.positions[baseGlyphIndex];
    var markPos = this.positions[this.glyphIterator.index];

    markPos.xOffset = baseCoords.x - markCoords.x;
    markPos.yOffset = baseCoords.y - markCoords.y;
    this.glyphIterator.cur.markAttachment = baseGlyphIndex;
  };

  GPOSProcessor.prototype.getAnchor = function getAnchor(anchor) {
    // TODO: contour point, device tables
    var x = anchor.xCoordinate;
    var y = anchor.yCoordinate;

    // Adjustments for font variations
    var variationProcessor = this.font._variationProcessor;
    var variationStore = this.font.GDEF && this.font.GDEF.itemVariationStore;
    if (variationProcessor && variationStore) {
      if (anchor.xDeviceTable) {
        x += variationProcessor.getDelta(variationStore, anchor.xDeviceTable.a, anchor.xDeviceTable.b);
      }

      if (anchor.yDeviceTable) {
        y += variationProcessor.getDelta(variationStore, anchor.yDeviceTable.a, anchor.yDeviceTable.b);
      }
    }

    return { x: x, y: y };
  };

  GPOSProcessor.prototype.applyFeatures = function applyFeatures(userFeatures, glyphs, advances) {
    _OTProcessor.prototype.applyFeatures.call(this, userFeatures, glyphs, advances);

    for (var i = 0; i < this.glyphs.length; i++) {
      this.fixCursiveAttachment(i);
    }

    this.fixMarkAttachment();
  };

  GPOSProcessor.prototype.fixCursiveAttachment = function fixCursiveAttachment(i) {
    var glyph = this.glyphs[i];
    if (glyph.cursiveAttachment != null) {
      var j = glyph.cursiveAttachment;

      glyph.cursiveAttachment = null;
      this.fixCursiveAttachment(j);

      this.positions[i].yOffset += this.positions[j].yOffset;
    }
  };

  GPOSProcessor.prototype.fixMarkAttachment = function fixMarkAttachment() {
    for (var i = 0; i < this.glyphs.length; i++) {
      var glyph = this.glyphs[i];
      if (glyph.markAttachment != null) {
        var j = glyph.markAttachment;

        this.positions[i].xOffset += this.positions[j].xOffset;
        this.positions[i].yOffset += this.positions[j].yOffset;

        if (this.direction === 'ltr') {
          for (var k = j; k < i; k++) {
            this.positions[i].xOffset -= this.positions[k].xAdvance;
            this.positions[i].yOffset -= this.positions[k].yAdvance;
          }
        } else {
          for (var _k = j + 1; _k < i + 1; _k++) {
            this.positions[i].xOffset += this.positions[_k].xAdvance;
            this.positions[i].yOffset += this.positions[_k].yAdvance;
          }
        }
      }
    }
  };

  return GPOSProcessor;
}(OTProcessor);

var OTLayoutEngine = function () {
  function OTLayoutEngine(font) {
    _classCallCheck(this, OTLayoutEngine);

    this.font = font;
    this.glyphInfos = null;
    this.plan = null;
    this.GSUBProcessor = null;
    this.GPOSProcessor = null;
    this.fallbackPosition = true;

    if (font.GSUB) {
      this.GSUBProcessor = new GSUBProcessor(font, font.GSUB);
    }

    if (font.GPOS) {
      this.GPOSProcessor = new GPOSProcessor(font, font.GPOS);
    }
  }

  OTLayoutEngine.prototype.setup = function setup(glyphRun) {
    var _this = this;

    // Map glyphs to GlyphInfo objects so data can be passed between
    // GSUB and GPOS without mutating the real (shared) Glyph objects.
    this.glyphInfos = glyphRun.glyphs.map(function (glyph) {
      return new GlyphInfo(_this.font, glyph.id, [].concat(glyph.codePoints));
    });

    // Select a script based on what is available in GSUB/GPOS.
    var script = null;
    if (this.GPOSProcessor) {
      script = this.GPOSProcessor.selectScript(glyphRun.script, glyphRun.language, glyphRun.direction);
    }

    if (this.GSUBProcessor) {
      script = this.GSUBProcessor.selectScript(glyphRun.script, glyphRun.language, glyphRun.direction);
    }

    // Choose a shaper based on the script, and setup a shaping plan.
    // This determines which features to apply to which glyphs.
    this.shaper = choose(script);
    this.plan = new ShapingPlan(this.font, script, glyphRun.direction);
    this.shaper.plan(this.plan, this.glyphInfos, glyphRun.features);

    // Assign chosen features to output glyph run
    for (var key in this.plan.allFeatures) {
      glyphRun.features[key] = true;
    }
  };

  OTLayoutEngine.prototype.substitute = function substitute(glyphRun) {
    var _this2 = this;

    if (this.GSUBProcessor) {
      this.plan.process(this.GSUBProcessor, this.glyphInfos);

      // Map glyph infos back to normal Glyph objects
      glyphRun.glyphs = this.glyphInfos.map(function (glyphInfo) {
        return _this2.font.getGlyph(glyphInfo.id, glyphInfo.codePoints);
      });
    }
  };

  OTLayoutEngine.prototype.position = function position(glyphRun) {
    if (this.shaper.zeroMarkWidths === 'BEFORE_GPOS') {
      this.zeroMarkAdvances(glyphRun.positions);
    }

    if (this.GPOSProcessor) {
      this.plan.process(this.GPOSProcessor, this.glyphInfos, glyphRun.positions);
    }

    if (this.shaper.zeroMarkWidths === 'AFTER_GPOS') {
      this.zeroMarkAdvances(glyphRun.positions);
    }

    // Reverse the glyphs and positions if the script is right-to-left
    if (glyphRun.direction === 'rtl') {
      glyphRun.glyphs.reverse();
      glyphRun.positions.reverse();
    }

    return this.GPOSProcessor && this.GPOSProcessor.features;
  };

  OTLayoutEngine.prototype.zeroMarkAdvances = function zeroMarkAdvances(positions) {
    for (var i = 0; i < this.glyphInfos.length; i++) {
      if (this.glyphInfos[i].isMark) {
        positions[i].xAdvance = 0;
        positions[i].yAdvance = 0;
      }
    }
  };

  OTLayoutEngine.prototype.cleanup = function cleanup() {
    this.glyphInfos = null;
    this.plan = null;
    this.shaper = null;
  };

  OTLayoutEngine.prototype.getAvailableFeatures = function getAvailableFeatures(script, language) {
    var features = [];

    if (this.GSUBProcessor) {
      this.GSUBProcessor.selectScript(script, language);
      features.push.apply(features, _Object$keys(this.GSUBProcessor.features));
    }

    if (this.GPOSProcessor) {
      this.GPOSProcessor.selectScript(script, language);
      features.push.apply(features, _Object$keys(this.GPOSProcessor.features));
    }

    return features;
  };

  return OTLayoutEngine;
}();

var LayoutEngine = function () {
  function LayoutEngine(font) {
    _classCallCheck(this, LayoutEngine);

    this.font = font;
    this.unicodeLayoutEngine = null;
    this.kernProcessor = null;

    // Choose an advanced layout engine. We try the AAT morx table first since more
    // scripts are currently supported because the shaping logic is built into the font.
    if (this.font.morx) {
      this.engine = new AATLayoutEngine(this.font);
    } else if (this.font.GSUB || this.font.GPOS) {
      this.engine = new OTLayoutEngine(this.font);
    }
  }

  LayoutEngine.prototype.layout = function layout(string, features, script, language, direction) {
    // Make the features parameter optional
    if (typeof features === 'string') {
      direction = language;
      language = script;
      script = features;
      features = [];
    }

    // Map string to glyphs if needed
    if (typeof string === 'string') {
      // Attempt to detect the script from the string if not provided.
      if (script == null) {
        script = forString(string);
      }

      var glyphs = this.font.glyphsForString(string);
    } else {
      // Attempt to detect the script from the glyph code points if not provided.
      if (script == null) {
        var codePoints = [];
        for (var _iterator = string, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
          var _ref;

          if (_isArray) {
            if (_i >= _iterator.length) break;
            _ref = _iterator[_i++];
          } else {
            _i = _iterator.next();
            if (_i.done) break;
            _ref = _i.value;
          }

          var glyph = _ref;

          codePoints.push.apply(codePoints, glyph.codePoints);
        }

        script = forCodePoints(codePoints);
      }

      var glyphs = string;
    }

    var glyphRun = new GlyphRun(glyphs, features, script, language, direction);

    // Return early if there are no glyphs
    if (glyphs.length === 0) {
      glyphRun.positions = [];
      return glyphRun;
    }

    // Setup the advanced layout engine
    if (this.engine && this.engine.setup) {
      this.engine.setup(glyphRun);
    }

    // Substitute and position the glyphs
    this.substitute(glyphRun);
    this.position(glyphRun);

    this.hideDefaultIgnorables(glyphRun.glyphs, glyphRun.positions);

    // Let the layout engine clean up any state it might have
    if (this.engine && this.engine.cleanup) {
      this.engine.cleanup();
    }

    return glyphRun;
  };

  LayoutEngine.prototype.substitute = function substitute(glyphRun) {
    // Call the advanced layout engine to make substitutions
    if (this.engine && this.engine.substitute) {
      this.engine.substitute(glyphRun);
    }
  };

  LayoutEngine.prototype.position = function position(glyphRun) {
    // Get initial glyph positions
    glyphRun.positions = glyphRun.glyphs.map(function (glyph) {
      return new GlyphPosition(glyph.advanceWidth);
    });
    var positioned = null;

    // Call the advanced layout engine. Returns the features applied.
    if (this.engine && this.engine.position) {
      positioned = this.engine.position(glyphRun);
    }

    // if there is no GPOS table, use unicode properties to position marks.
    if (!positioned && (!this.engine || this.engine.fallbackPosition)) {
      if (!this.unicodeLayoutEngine) {
        this.unicodeLayoutEngine = new UnicodeLayoutEngine(this.font);
      }

      this.unicodeLayoutEngine.positionGlyphs(glyphRun.glyphs, glyphRun.positions);
    }

    // if kerning is not supported by GPOS, do kerning with the TrueType/AAT kern table
    if ((!positioned || !positioned.kern) && glyphRun.features.kern !== false && this.font.kern) {
      if (!this.kernProcessor) {
        this.kernProcessor = new KernProcessor(this.font);
      }

      this.kernProcessor.process(glyphRun.glyphs, glyphRun.positions);
      glyphRun.features.kern = true;
    }
  };

  LayoutEngine.prototype.hideDefaultIgnorables = function hideDefaultIgnorables(glyphs, positions) {
    var space = this.font.glyphForCodePoint(0x20);
    for (var i = 0; i < glyphs.length; i++) {
      if (this.isDefaultIgnorable(glyphs[i].codePoints[0])) {
        glyphs[i] = space;
        positions[i].xAdvance = 0;
        positions[i].yAdvance = 0;
      }
    }
  };

  LayoutEngine.prototype.isDefaultIgnorable = function isDefaultIgnorable(ch) {
    // From DerivedCoreProperties.txt in the Unicode database,
    // minus U+115F, U+1160, U+3164 and U+FFA0, which is what
    // Harfbuzz and Uniscribe do.
    var plane = ch >> 16;
    if (plane === 0) {
      // BMP
      switch (ch >> 8) {
        case 0x00:
          return ch === 0x00AD;
        case 0x03:
          return ch === 0x034F;
        case 0x06:
          return ch === 0x061C;
        case 0x17:
          return 0x17B4 <= ch && ch <= 0x17B5;
        case 0x18:
          return 0x180B <= ch && ch <= 0x180E;
        case 0x20:
          return 0x200B <= ch && ch <= 0x200F || 0x202A <= ch && ch <= 0x202E || 0x2060 <= ch && ch <= 0x206F;
        case 0xFE:
          return 0xFE00 <= ch && ch <= 0xFE0F || ch === 0xFEFF;
        case 0xFF:
          return 0xFFF0 <= ch && ch <= 0xFFF8;
        default:
          return false;
      }
    } else {
      // Other planes
      switch (plane) {
        case 0x01:
          return 0x1BCA0 <= ch && ch <= 0x1BCA3 || 0x1D173 <= ch && ch <= 0x1D17A;
        case 0x0E:
          return 0xE0000 <= ch && ch <= 0xE0FFF;
        default:
          return false;
      }
    }
  };

  LayoutEngine.prototype.getAvailableFeatures = function getAvailableFeatures(script, language) {
    var features = [];

    if (this.engine) {
      features.push.apply(features, this.engine.getAvailableFeatures(script, language));
    }

    if (this.font.kern && features.indexOf('kern') === -1) {
      features.push('kern');
    }

    return features;
  };

  LayoutEngine.prototype.stringsForGlyph = function stringsForGlyph(gid) {
    var result = new _Set();

    var codePoints = this.font._cmapProcessor.codePointsForGlyph(gid);
    for (var _iterator2 = codePoints, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
      var _ref2;

      if (_isArray2) {
        if (_i2 >= _iterator2.length) break;
        _ref2 = _iterator2[_i2++];
      } else {
        _i2 = _iterator2.next();
        if (_i2.done) break;
        _ref2 = _i2.value;
      }

      var codePoint = _ref2;

      result.add(_String$fromCodePoint(codePoint));
    }

    if (this.engine && this.engine.stringsForGlyph) {
      for (var _iterator3 = this.engine.stringsForGlyph(gid), _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
        var _ref3;

        if (_isArray3) {
          if (_i3 >= _iterator3.length) break;
          _ref3 = _iterator3[_i3++];
        } else {
          _i3 = _iterator3.next();
          if (_i3.done) break;
          _ref3 = _i3.value;
        }

        var string = _ref3;

        result.add(string);
      }
    }

    return _Array$from(result);
  };

  return LayoutEngine;
}();

var SVG_COMMANDS = {
  moveTo: 'M',
  lineTo: 'L',
  quadraticCurveTo: 'Q',
  bezierCurveTo: 'C',
  closePath: 'Z'
};

/**
 * Path objects are returned by glyphs and represent the actual
 * vector outlines for each glyph in the font. Paths can be converted
 * to SVG path data strings, or to functions that can be applied to
 * render the path to a graphics context.
 */

var Path = function () {
  function Path() {
    _classCallCheck(this, Path);

    this.commands = [];
    this._bbox = null;
    this._cbox = null;
  }

  /**
   * Compiles the path to a JavaScript function that can be applied with
   * a graphics context in order to render the path.
   * @return {string}
   */


  Path.prototype.toFunction = function toFunction() {
    var _this = this;

    return function (ctx) {
      _this.commands.forEach(function (c) {
        return ctx[c.command].apply(ctx, c.args);
      });
    };
  };

  /**
   * Converts the path to an SVG path data string
   * @return {string}
   */


  Path.prototype.toSVG = function toSVG() {
    var cmds = this.commands.map(function (c) {
      var args = c.args.map(function (arg) {
        return Math.round(arg * 100) / 100;
      });
      return '' + SVG_COMMANDS[c.command] + args.join(' ');
    });

    return cmds.join('');
  };

  /**
   * Gets the "control box" of a path.
   * This is like the bounding box, but it includes all points including
   * control points of bezier segments and is much faster to compute than
   * the real bounding box.
   * @type {BBox}
   */


  /**
   * Applies a mapping function to each point in the path.
   * @param {function} fn
   * @return {Path}
   */
  Path.prototype.mapPoints = function mapPoints(fn) {
    var path = new Path();

    for (var _iterator = this.commands, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var c = _ref;

      var args = [];
      for (var _i2 = 0; _i2 < c.args.length; _i2 += 2) {
        var _fn = fn(c.args[_i2], c.args[_i2 + 1]),
            x = _fn[0],
            y = _fn[1];

        args.push(x, y);
      }

      path[c.command].apply(path, args);
    }

    return path;
  };

  /**
   * Transforms the path by the given matrix.
   */


  Path.prototype.transform = function transform(m0, m1, m2, m3, m4, m5) {
    return this.mapPoints(function (x, y) {
      x = m0 * x + m2 * y + m4;
      y = m1 * x + m3 * y + m5;
      return [x, y];
    });
  };

  /**
   * Translates the path by the given offset.
   */


  Path.prototype.translate = function translate(x, y) {
    return this.transform(1, 0, 0, 1, x, y);
  };

  /**
   * Rotates the path by the given angle (in radians).
   */


  Path.prototype.rotate = function rotate(angle) {
    var cos = Math.cos(angle);
    var sin = Math.sin(angle);
    return this.transform(cos, sin, -sin, cos, 0, 0);
  };

  /**
   * Scales the path.
   */


  Path.prototype.scale = function scale(scaleX) {
    var scaleY = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : scaleX;

    return this.transform(scaleX, 0, 0, scaleY, 0, 0);
  };

  _createClass(Path, [{
    key: 'cbox',
    get: function get() {
      if (!this._cbox) {
        var cbox = new BBox();
        for (var _iterator2 = this.commands, _isArray2 = Array.isArray(_iterator2), _i3 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
          var _ref2;

          if (_isArray2) {
            if (_i3 >= _iterator2.length) break;
            _ref2 = _iterator2[_i3++];
          } else {
            _i3 = _iterator2.next();
            if (_i3.done) break;
            _ref2 = _i3.value;
          }

          var command = _ref2;

          for (var _i4 = 0; _i4 < command.args.length; _i4 += 2) {
            cbox.addPoint(command.args[_i4], command.args[_i4 + 1]);
          }
        }

        this._cbox = _Object$freeze(cbox);
      }

      return this._cbox;
    }

    /**
     * Gets the exact bounding box of the path by evaluating curve segments.
     * Slower to compute than the control box, but more accurate.
     * @type {BBox}
     */

  }, {
    key: 'bbox',
    get: function get() {
      if (this._bbox) {
        return this._bbox;
      }

      var bbox = new BBox();
      var cx = 0,
          cy = 0;

      var f = function f(t) {
        return Math.pow(1 - t, 3) * p0[i] + 3 * Math.pow(1 - t, 2) * t * p1[i] + 3 * (1 - t) * Math.pow(t, 2) * p2[i] + Math.pow(t, 3) * p3[i];
      };

      for (var _iterator3 = this.commands, _isArray3 = Array.isArray(_iterator3), _i5 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
        var _ref3;

        if (_isArray3) {
          if (_i5 >= _iterator3.length) break;
          _ref3 = _iterator3[_i5++];
        } else {
          _i5 = _iterator3.next();
          if (_i5.done) break;
          _ref3 = _i5.value;
        }

        var c = _ref3;

        switch (c.command) {
          case 'moveTo':
          case 'lineTo':
            var _c$args = c.args,
                x = _c$args[0],
                y = _c$args[1];

            bbox.addPoint(x, y);
            cx = x;
            cy = y;
            break;

          case 'quadraticCurveTo':
          case 'bezierCurveTo':
            if (c.command === 'quadraticCurveTo') {
              // http://fontforge.org/bezier.html
              var _c$args2 = c.args,
                  qp1x = _c$args2[0],
                  qp1y = _c$args2[1],
                  p3x = _c$args2[2],
                  p3y = _c$args2[3];

              var cp1x = cx + 2 / 3 * (qp1x - cx); // CP1 = QP0 + 2/3 * (QP1-QP0)
              var cp1y = cy + 2 / 3 * (qp1y - cy);
              var cp2x = p3x + 2 / 3 * (qp1x - p3x); // CP2 = QP2 + 2/3 * (QP1-QP2)
              var cp2y = p3y + 2 / 3 * (qp1y - p3y);
            } else {
              var _c$args3 = c.args,
                  cp1x = _c$args3[0],
                  cp1y = _c$args3[1],
                  cp2x = _c$args3[2],
                  cp2y = _c$args3[3],
                  p3x = _c$args3[4],
                  p3y = _c$args3[5];
            }

            // http://blog.hackers-cafe.net/2009/06/how-to-calculate-bezier-curves-bounding.html
            bbox.addPoint(p3x, p3y);

            var p0 = [cx, cy];
            var p1 = [cp1x, cp1y];
            var p2 = [cp2x, cp2y];
            var p3 = [p3x, p3y];

            for (var i = 0; i <= 1; i++) {
              var b = 6 * p0[i] - 12 * p1[i] + 6 * p2[i];
              var a = -3 * p0[i] + 9 * p1[i] - 9 * p2[i] + 3 * p3[i];
              c = 3 * p1[i] - 3 * p0[i];

              if (a === 0) {
                if (b === 0) {
                  continue;
                }

                var t = -c / b;
                if (0 < t && t < 1) {
                  if (i === 0) {
                    bbox.addPoint(f(t), bbox.maxY);
                  } else if (i === 1) {
                    bbox.addPoint(bbox.maxX, f(t));
                  }
                }

                continue;
              }

              var b2ac = Math.pow(b, 2) - 4 * c * a;
              if (b2ac < 0) {
                continue;
              }

              var t1 = (-b + Math.sqrt(b2ac)) / (2 * a);
              if (0 < t1 && t1 < 1) {
                if (i === 0) {
                  bbox.addPoint(f(t1), bbox.maxY);
                } else if (i === 1) {
                  bbox.addPoint(bbox.maxX, f(t1));
                }
              }

              var t2 = (-b - Math.sqrt(b2ac)) / (2 * a);
              if (0 < t2 && t2 < 1) {
                if (i === 0) {
                  bbox.addPoint(f(t2), bbox.maxY);
                } else if (i === 1) {
                  bbox.addPoint(bbox.maxX, f(t2));
                }
              }
            }

            cx = p3x;
            cy = p3y;
            break;
        }
      }

      return this._bbox = _Object$freeze(bbox);
    }
  }]);

  return Path;
}();

var _arr = ['moveTo', 'lineTo', 'quadraticCurveTo', 'bezierCurveTo', 'closePath'];

var _loop = function _loop() {
  var command = _arr[_i6];
  Path.prototype[command] = function () {
    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    this._bbox = this._cbox = null;
    this.commands.push({
      command: command,
      args: args
    });

    return this;
  };
};

for (var _i6 = 0; _i6 < _arr.length; _i6++) {
  _loop();
}

var StandardNames = ['.notdef', '.null', 'nonmarkingreturn', 'space', 'exclam', 'quotedbl', 'numbersign', 'dollar', 'percent', 'ampersand', 'quotesingle', 'parenleft', 'parenright', 'asterisk', 'plus', 'comma', 'hyphen', 'period', 'slash', 'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'colon', 'semicolon', 'less', 'equal', 'greater', 'question', 'at', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'bracketleft', 'backslash', 'bracketright', 'asciicircum', 'underscore', 'grave', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'braceleft', 'bar', 'braceright', 'asciitilde', 'Adieresis', 'Aring', 'Ccedilla', 'Eacute', 'Ntilde', 'Odieresis', 'Udieresis', 'aacute', 'agrave', 'acircumflex', 'adieresis', 'atilde', 'aring', 'ccedilla', 'eacute', 'egrave', 'ecircumflex', 'edieresis', 'iacute', 'igrave', 'icircumflex', 'idieresis', 'ntilde', 'oacute', 'ograve', 'ocircumflex', 'odieresis', 'otilde', 'uacute', 'ugrave', 'ucircumflex', 'udieresis', 'dagger', 'degree', 'cent', 'sterling', 'section', 'bullet', 'paragraph', 'germandbls', 'registered', 'copyright', 'trademark', 'acute', 'dieresis', 'notequal', 'AE', 'Oslash', 'infinity', 'plusminus', 'lessequal', 'greaterequal', 'yen', 'mu', 'partialdiff', 'summation', 'product', 'pi', 'integral', 'ordfeminine', 'ordmasculine', 'Omega', 'ae', 'oslash', 'questiondown', 'exclamdown', 'logicalnot', 'radical', 'florin', 'approxequal', 'Delta', 'guillemotleft', 'guillemotright', 'ellipsis', 'nonbreakingspace', 'Agrave', 'Atilde', 'Otilde', 'OE', 'oe', 'endash', 'emdash', 'quotedblleft', 'quotedblright', 'quoteleft', 'quoteright', 'divide', 'lozenge', 'ydieresis', 'Ydieresis', 'fraction', 'currency', 'guilsinglleft', 'guilsinglright', 'fi', 'fl', 'daggerdbl', 'periodcentered', 'quotesinglbase', 'quotedblbase', 'perthousand', 'Acircumflex', 'Ecircumflex', 'Aacute', 'Edieresis', 'Egrave', 'Iacute', 'Icircumflex', 'Idieresis', 'Igrave', 'Oacute', 'Ocircumflex', 'apple', 'Ograve', 'Uacute', 'Ucircumflex', 'Ugrave', 'dotlessi', 'circumflex', 'tilde', 'macron', 'breve', 'dotaccent', 'ring', 'cedilla', 'hungarumlaut', 'ogonek', 'caron', 'Lslash', 'lslash', 'Scaron', 'scaron', 'Zcaron', 'zcaron', 'brokenbar', 'Eth', 'eth', 'Yacute', 'yacute', 'Thorn', 'thorn', 'minus', 'multiply', 'onesuperior', 'twosuperior', 'threesuperior', 'onehalf', 'onequarter', 'threequarters', 'franc', 'Gbreve', 'gbreve', 'Idotaccent', 'Scedilla', 'scedilla', 'Cacute', 'cacute', 'Ccaron', 'ccaron', 'dcroat'];

var _class$8;
function _applyDecoratedDescriptor$4(target, property, decorators, descriptor, context) {
  var desc = {};
  Object['ke' + 'ys'](descriptor).forEach(function (key) {
    desc[key] = descriptor[key];
  });
  desc.enumerable = !!desc.enumerable;
  desc.configurable = !!desc.configurable;

  if ('value' in desc || desc.initializer) {
    desc.writable = true;
  }

  desc = decorators.slice().reverse().reduce(function (desc, decorator) {
    return decorator(target, property, desc) || desc;
  }, desc);

  if (context && desc.initializer !== void 0) {
    desc.value = desc.initializer ? desc.initializer.call(context) : void 0;
    desc.initializer = undefined;
  }

  if (desc.initializer === void 0) {
    Object['define' + 'Property'](target, property, desc);
    desc = null;
  }

  return desc;
}

/**
 * Glyph objects represent a glyph in the font. They have various properties for accessing metrics and
 * the actual vector path the glyph represents, and methods for rendering the glyph to a graphics context.
 *
 * You do not create glyph objects directly. They are created by various methods on the font object.
 * There are several subclasses of the base Glyph class internally that may be returned depending
 * on the font format, but they all inherit from this class.
 */
var Glyph = (_class$8 = function () {
  function Glyph(id, codePoints, font) {
    _classCallCheck(this, Glyph);

    /**
     * The glyph id in the font
     * @type {number}
     */
    this.id = id;

    /**
     * An array of unicode code points that are represented by this glyph.
     * There can be multiple code points in the case of ligatures and other glyphs
     * that represent multiple visual characters.
     * @type {number[]}
     */
    this.codePoints = codePoints;
    this._font = font;

    // TODO: get this info from GDEF if available
    this.isMark = this.codePoints.length > 0 && this.codePoints.every(unicode.isMark);
    this.isLigature = this.codePoints.length > 1;
  }

  Glyph.prototype._getPath = function _getPath() {
    return new Path();
  };

  Glyph.prototype._getCBox = function _getCBox() {
    return this.path.cbox;
  };

  Glyph.prototype._getBBox = function _getBBox() {
    return this.path.bbox;
  };

  Glyph.prototype._getTableMetrics = function _getTableMetrics(table) {
    if (this.id < table.metrics.length) {
      return table.metrics.get(this.id);
    }

    var metric = table.metrics.get(table.metrics.length - 1);
    var res = {
      advance: metric ? metric.advance : 0,
      bearing: table.bearings.get(this.id - table.metrics.length) || 0
    };

    return res;
  };

  Glyph.prototype._getMetrics = function _getMetrics(cbox) {
    if (this._metrics) {
      return this._metrics;
    }

    var _getTableMetrics2 = this._getTableMetrics(this._font.hmtx),
        advanceWidth = _getTableMetrics2.advance,
        leftBearing = _getTableMetrics2.bearing;

    // For vertical metrics, use vmtx if available, or fall back to global data from OS/2 or hhea


    if (this._font.vmtx) {
      var _getTableMetrics3 = this._getTableMetrics(this._font.vmtx),
          advanceHeight = _getTableMetrics3.advance,
          topBearing = _getTableMetrics3.bearing;
    } else {
      var os2 = void 0;
      if (typeof cbox === 'undefined' || cbox === null) {
        cbox = this.cbox;
      }

      if ((os2 = this._font['OS/2']) && os2.version > 0) {
        var advanceHeight = Math.abs(os2.typoAscender - os2.typoDescender);
        var topBearing = os2.typoAscender - cbox.maxY;
      } else {
        var hhea = this._font.hhea;

        var advanceHeight = Math.abs(hhea.ascent - hhea.descent);
        var topBearing = hhea.ascent - cbox.maxY;
      }
    }

    if (this._font._variationProcessor && this._font.HVAR) {
      advanceWidth += this._font._variationProcessor.getAdvanceAdjustment(this.id, this._font.HVAR);
    }

    return this._metrics = { advanceWidth: advanceWidth, advanceHeight: advanceHeight, leftBearing: leftBearing, topBearing: topBearing };
  };

  /**
   * The glyph’s control box.
   * This is often the same as the bounding box, but is faster to compute.
   * Because of the way bezier curves are defined, some of the control points
   * can be outside of the bounding box. Where `bbox` takes this into account,
   * `cbox` does not. Thus, cbox is less accurate, but faster to compute.
   * See [here](http://www.freetype.org/freetype2/docs/glyphs/glyphs-6.html#section-2)
   * for a more detailed description.
   *
   * @type {BBox}
   */


  /**
   * Returns a path scaled to the given font size.
   * @param {number} size
   * @return {Path}
   */
  Glyph.prototype.getScaledPath = function getScaledPath(size) {
    var scale = 1 / this._font.unitsPerEm * size;
    return this.path.scale(scale);
  };

  /**
   * The glyph's advance width.
   * @type {number}
   */


  Glyph.prototype._getName = function _getName() {
    var post = this._font.post;

    if (!post) {
      return null;
    }

    switch (post.version) {
      case 1:
        return StandardNames[this.id];

      case 2:
        var id = post.glyphNameIndex[this.id];
        if (id < StandardNames.length) {
          return StandardNames[id];
        }

        return post.names[id - StandardNames.length];

      case 2.5:
        return StandardNames[this.id + post.offsets[this.id]];

      case 4:
        return String.fromCharCode(post.map[this.id]);
    }
  };

  /**
   * The glyph's name
   * @type {string}
   */


  /**
   * Renders the glyph to the given graphics context, at the specified font size.
   * @param {CanvasRenderingContext2d} ctx
   * @param {number} size
   */
  Glyph.prototype.render = function render(ctx, size) {
    ctx.save();

    var scale = 1 / this._font.head.unitsPerEm * size;
    ctx.scale(scale, scale);

    var fn = this.path.toFunction();
    fn(ctx);
    ctx.fill();

    ctx.restore();
  };

  _createClass(Glyph, [{
    key: 'cbox',
    get: function get() {
      return this._getCBox();
    }

    /**
     * The glyph’s bounding box, i.e. the rectangle that encloses the
     * glyph outline as tightly as possible.
     * @type {BBox}
     */

  }, {
    key: 'bbox',
    get: function get() {
      return this._getBBox();
    }

    /**
     * A vector Path object representing the glyph outline.
     * @type {Path}
     */

  }, {
    key: 'path',
    get: function get() {
      // Cache the path so we only decode it once
      // Decoding is actually performed by subclasses
      return this._getPath();
    }
  }, {
    key: 'advanceWidth',
    get: function get() {
      return this._getMetrics().advanceWidth;
    }

    /**
     * The glyph's advance height.
     * @type {number}
     */

  }, {
    key: 'advanceHeight',
    get: function get() {
      return this._getMetrics().advanceHeight;
    }
  }, {
    key: 'ligatureCaretPositions',
    get: function get() {}
  }, {
    key: 'name',
    get: function get() {
      return this._getName();
    }
  }]);

  return Glyph;
}(), (_applyDecoratedDescriptor$4(_class$8.prototype, 'cbox', [cache], _Object$getOwnPropertyDescriptor(_class$8.prototype, 'cbox'), _class$8.prototype), _applyDecoratedDescriptor$4(_class$8.prototype, 'bbox', [cache], _Object$getOwnPropertyDescriptor(_class$8.prototype, 'bbox'), _class$8.prototype), _applyDecoratedDescriptor$4(_class$8.prototype, 'path', [cache], _Object$getOwnPropertyDescriptor(_class$8.prototype, 'path'), _class$8.prototype), _applyDecoratedDescriptor$4(_class$8.prototype, 'advanceWidth', [cache], _Object$getOwnPropertyDescriptor(_class$8.prototype, 'advanceWidth'), _class$8.prototype), _applyDecoratedDescriptor$4(_class$8.prototype, 'advanceHeight', [cache], _Object$getOwnPropertyDescriptor(_class$8.prototype, 'advanceHeight'), _class$8.prototype), _applyDecoratedDescriptor$4(_class$8.prototype, 'name', [cache], _Object$getOwnPropertyDescriptor(_class$8.prototype, 'name'), _class$8.prototype)), _class$8);

// The header for both simple and composite glyphs
var GlyfHeader = new r.Struct({
  numberOfContours: r.int16, // if negative, this is a composite glyph
  xMin: r.int16,
  yMin: r.int16,
  xMax: r.int16,
  yMax: r.int16
});

// Flags for simple glyphs
var ON_CURVE = 1 << 0;
var X_SHORT_VECTOR = 1 << 1;
var Y_SHORT_VECTOR = 1 << 2;
var REPEAT = 1 << 3;
var SAME_X = 1 << 4;
var SAME_Y = 1 << 5;

// Flags for composite glyphs
var ARG_1_AND_2_ARE_WORDS = 1 << 0;
var WE_HAVE_A_SCALE = 1 << 3;
var MORE_COMPONENTS = 1 << 5;
var WE_HAVE_AN_X_AND_Y_SCALE = 1 << 6;
var WE_HAVE_A_TWO_BY_TWO = 1 << 7;
var WE_HAVE_INSTRUCTIONS = 1 << 8;
// Represents a point in a simple glyph
var Point = function () {
  function Point(onCurve, endContour) {
    var x = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    var y = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;

    _classCallCheck(this, Point);

    this.onCurve = onCurve;
    this.endContour = endContour;
    this.x = x;
    this.y = y;
  }

  Point.prototype.copy = function copy() {
    return new Point(this.onCurve, this.endContour, this.x, this.y);
  };

  return Point;
}();

// Represents a component in a composite glyph

var Component = function Component(glyphID, dx, dy) {
  _classCallCheck(this, Component);

  this.glyphID = glyphID;
  this.dx = dx;
  this.dy = dy;
  this.pos = 0;
  this.scaleX = this.scaleY = 1;
  this.scale01 = this.scale10 = 0;
};

/**
 * Represents a TrueType glyph.
 */


var TTFGlyph = function (_Glyph) {
  _inherits(TTFGlyph, _Glyph);

  function TTFGlyph() {
    _classCallCheck(this, TTFGlyph);

    return _possibleConstructorReturn(this, _Glyph.apply(this, arguments));
  }

  // Parses just the glyph header and returns the bounding box
  TTFGlyph.prototype._getCBox = function _getCBox(internal) {
    // We need to decode the glyph if variation processing is requested,
    // so it's easier just to recompute the path's cbox after decoding.
    if (this._font._variationProcessor && !internal) {
      return this.path.cbox;
    }

    var stream = this._font._getTableStream('glyf');
    stream.pos += this._font.loca.offsets[this.id];
    var glyph = GlyfHeader.decode(stream);

    var cbox = new BBox(glyph.xMin, glyph.yMin, glyph.xMax, glyph.yMax);
    return _Object$freeze(cbox);
  };

  // Parses a single glyph coordinate


  TTFGlyph.prototype._parseGlyphCoord = function _parseGlyphCoord(stream, prev, short, same) {
    if (short) {
      var val = stream.readUInt8();
      if (!same) {
        val = -val;
      }

      val += prev;
    } else {
      if (same) {
        var val = prev;
      } else {
        var val = prev + stream.readInt16BE();
      }
    }

    return val;
  };

  // Decodes the glyph data into points for simple glyphs,
  // or components for composite glyphs


  TTFGlyph.prototype._decode = function _decode() {
    var glyfPos = this._font.loca.offsets[this.id];
    var nextPos = this._font.loca.offsets[this.id + 1];

    // Nothing to do if there is no data for this glyph
    if (glyfPos === nextPos) {
      return null;
    }

    var stream = this._font._getTableStream('glyf');
    stream.pos += glyfPos;
    var startPos = stream.pos;

    var glyph = GlyfHeader.decode(stream);

    if (glyph.numberOfContours > 0) {
      this._decodeSimple(glyph, stream);
    } else if (glyph.numberOfContours < 0) {
      this._decodeComposite(glyph, stream, startPos);
    }

    return glyph;
  };

  TTFGlyph.prototype._decodeSimple = function _decodeSimple(glyph, stream) {
    // this is a simple glyph
    glyph.points = [];

    var endPtsOfContours = new r.Array(r.uint16, glyph.numberOfContours).decode(stream);
    glyph.instructions = new r.Array(r.uint8, r.uint16).decode(stream);

    var flags = [];
    var numCoords = endPtsOfContours[endPtsOfContours.length - 1] + 1;

    while (flags.length < numCoords) {
      var flag = stream.readUInt8();
      flags.push(flag);

      // check for repeat flag
      if (flag & REPEAT) {
        var count = stream.readUInt8();
        for (var j = 0; j < count; j++) {
          flags.push(flag);
        }
      }
    }

    for (var i = 0; i < flags.length; i++) {
      var flag = flags[i];
      var point = new Point(!!(flag & ON_CURVE), endPtsOfContours.indexOf(i) >= 0, 0, 0);
      glyph.points.push(point);
    }

    var px = 0;
    for (var i = 0; i < flags.length; i++) {
      var flag = flags[i];
      glyph.points[i].x = px = this._parseGlyphCoord(stream, px, flag & X_SHORT_VECTOR, flag & SAME_X);
    }

    var py = 0;
    for (var i = 0; i < flags.length; i++) {
      var flag = flags[i];
      glyph.points[i].y = py = this._parseGlyphCoord(stream, py, flag & Y_SHORT_VECTOR, flag & SAME_Y);
    }

    if (this._font._variationProcessor) {
      var points = glyph.points.slice();
      points.push.apply(points, this._getPhantomPoints(glyph));

      this._font._variationProcessor.transformPoints(this.id, points);
      glyph.phantomPoints = points.slice(-4);
    }

    return;
  };

  TTFGlyph.prototype._decodeComposite = function _decodeComposite(glyph, stream) {
    var offset = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;

    // this is a composite glyph
    glyph.components = [];
    var haveInstructions = false;
    var flags = MORE_COMPONENTS;

    while (flags & MORE_COMPONENTS) {
      flags = stream.readUInt16BE();
      var gPos = stream.pos - offset;
      var glyphID = stream.readUInt16BE();
      if (!haveInstructions) {
        haveInstructions = (flags & WE_HAVE_INSTRUCTIONS) !== 0;
      }

      if (flags & ARG_1_AND_2_ARE_WORDS) {
        var dx = stream.readInt16BE();
        var dy = stream.readInt16BE();
      } else {
        var dx = stream.readInt8();
        var dy = stream.readInt8();
      }

      var component = new Component(glyphID, dx, dy);
      component.pos = gPos;

      if (flags & WE_HAVE_A_SCALE) {
        // fixed number with 14 bits of fraction
        component.scaleX = component.scaleY = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
      } else if (flags & WE_HAVE_AN_X_AND_Y_SCALE) {
        component.scaleX = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
        component.scaleY = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
      } else if (flags & WE_HAVE_A_TWO_BY_TWO) {
        component.scaleX = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
        component.scale01 = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
        component.scale10 = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
        component.scaleY = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
      }

      glyph.components.push(component);
    }

    if (this._font._variationProcessor) {
      var points = [];
      for (var j = 0; j < glyph.components.length; j++) {
        var component = glyph.components[j];
        points.push(new Point(true, true, component.dx, component.dy));
      }

      points.push.apply(points, this._getPhantomPoints(glyph));

      this._font._variationProcessor.transformPoints(this.id, points);
      glyph.phantomPoints = points.splice(-4, 4);

      for (var i = 0; i < points.length; i++) {
        var point = points[i];
        glyph.components[i].dx = point.x;
        glyph.components[i].dy = point.y;
      }
    }

    return haveInstructions;
  };

  TTFGlyph.prototype._getPhantomPoints = function _getPhantomPoints(glyph) {
    var cbox = this._getCBox(true);
    if (this._metrics == null) {
      this._metrics = Glyph.prototype._getMetrics.call(this, cbox);
    }

    var _metrics = this._metrics,
        advanceWidth = _metrics.advanceWidth,
        advanceHeight = _metrics.advanceHeight,
        leftBearing = _metrics.leftBearing,
        topBearing = _metrics.topBearing;


    return [new Point(false, true, glyph.xMin - leftBearing, 0), new Point(false, true, glyph.xMin - leftBearing + advanceWidth, 0), new Point(false, true, 0, glyph.yMax + topBearing), new Point(false, true, 0, glyph.yMax + topBearing + advanceHeight)];
  };

  // Decodes font data, resolves composite glyphs, and returns an array of contours


  TTFGlyph.prototype._getContours = function _getContours() {
    var glyph = this._decode();
    if (!glyph) {
      return [];
    }

    var points = [];

    if (glyph.numberOfContours < 0) {
      // resolve composite glyphs
      for (var _iterator = glyph.components, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
        var _ref;

        if (_isArray) {
          if (_i >= _iterator.length) break;
          _ref = _iterator[_i++];
        } else {
          _i = _iterator.next();
          if (_i.done) break;
          _ref = _i.value;
        }

        var component = _ref;

        var _contours = this._font.getGlyph(component.glyphID)._getContours();
        for (var i = 0; i < _contours.length; i++) {
          var contour = _contours[i];
          for (var j = 0; j < contour.length; j++) {
            var _point = contour[j];
            var x = _point.x * component.scaleX + _point.y * component.scale01 + component.dx;
            var y = _point.y * component.scaleY + _point.x * component.scale10 + component.dy;
            points.push(new Point(_point.onCurve, _point.endContour, x, y));
          }
        }
      }
    } else {
      points = glyph.points || [];
    }

    // Recompute and cache metrics if we performed variation processing, and don't have an HVAR table
    if (glyph.phantomPoints && !this._font.directory.tables.HVAR) {
      this._metrics.advanceWidth = glyph.phantomPoints[1].x - glyph.phantomPoints[0].x;
      this._metrics.advanceHeight = glyph.phantomPoints[3].y - glyph.phantomPoints[2].y;
      this._metrics.leftBearing = glyph.xMin - glyph.phantomPoints[0].x;
      this._metrics.topBearing = glyph.phantomPoints[2].y - glyph.yMax;
    }

    var contours = [];
    var cur = [];
    for (var k = 0; k < points.length; k++) {
      var point = points[k];
      cur.push(point);
      if (point.endContour) {
        contours.push(cur);
        cur = [];
      }
    }

    return contours;
  };

  TTFGlyph.prototype._getMetrics = function _getMetrics() {
    if (this._metrics) {
      return this._metrics;
    }

    var cbox = this._getCBox(true);
    _Glyph.prototype._getMetrics.call(this, cbox);

    if (this._font._variationProcessor && !this._font.HVAR) {
      // No HVAR table, decode the glyph. This triggers recomputation of metrics.
      this.path;
    }

    return this._metrics;
  };

  // Converts contours to a Path object that can be rendered


  TTFGlyph.prototype._getPath = function _getPath() {
    var contours = this._getContours();
    var path = new Path();

    for (var i = 0; i < contours.length; i++) {
      var contour = contours[i];
      var firstPt = contour[0];
      var lastPt = contour[contour.length - 1];
      var start = 0;

      if (firstPt.onCurve) {
        // The first point will be consumed by the moveTo command, so skip in the loop
        var curvePt = null;
        start = 1;
      } else {
        if (lastPt.onCurve) {
          // Start at the last point if the first point is off curve and the last point is on curve
          firstPt = lastPt;
        } else {
          // Start at the middle if both the first and last points are off curve
          firstPt = new Point(false, false, (firstPt.x + lastPt.x) / 2, (firstPt.y + lastPt.y) / 2);
        }

        var curvePt = firstPt;
      }

      path.moveTo(firstPt.x, firstPt.y);

      for (var j = start; j < contour.length; j++) {
        var pt = contour[j];
        var prevPt = j === 0 ? firstPt : contour[j - 1];

        if (prevPt.onCurve && pt.onCurve) {
          path.lineTo(pt.x, pt.y);
        } else if (prevPt.onCurve && !pt.onCurve) {
          var curvePt = pt;
        } else if (!prevPt.onCurve && !pt.onCurve) {
          var midX = (prevPt.x + pt.x) / 2;
          var midY = (prevPt.y + pt.y) / 2;
          path.quadraticCurveTo(prevPt.x, prevPt.y, midX, midY);
          var curvePt = pt;
        } else if (!prevPt.onCurve && pt.onCurve) {
          path.quadraticCurveTo(curvePt.x, curvePt.y, pt.x, pt.y);
          var curvePt = null;
        } else {
          throw new Error("Unknown TTF path state");
        }
      }

      // Connect the first and last points
      if (curvePt) {
        path.quadraticCurveTo(curvePt.x, curvePt.y, firstPt.x, firstPt.y);
      }

      path.closePath();
    }

    return path;
  };

  return TTFGlyph;
}(Glyph);

/**
 * Represents an OpenType PostScript glyph, in the Compact Font Format.
 */

var CFFGlyph = function (_Glyph) {
  _inherits(CFFGlyph, _Glyph);

  function CFFGlyph() {
    _classCallCheck(this, CFFGlyph);

    return _possibleConstructorReturn(this, _Glyph.apply(this, arguments));
  }

  CFFGlyph.prototype._getName = function _getName() {
    if (this._font.CFF2) {
      return _Glyph.prototype._getName.call(this);
    }

    return this._font['CFF '].getGlyphName(this.id);
  };

  CFFGlyph.prototype.bias = function bias(s) {
    if (s.length < 1240) {
      return 107;
    } else if (s.length < 33900) {
      return 1131;
    } else {
      return 32768;
    }
  };

  CFFGlyph.prototype._getPath = function _getPath() {
    var cff = this._font.CFF2 || this._font['CFF '];
    var stream = cff.stream;

    var str = cff.topDict.CharStrings[this.id];
    var end = str.offset + str.length;
    stream.pos = str.offset;

    var path = new Path();
    var stack = [];
    var trans = [];

    var width = null;
    var nStems = 0;
    var x = 0,
        y = 0;
    var usedGsubrs = void 0;
    var usedSubrs = void 0;
    var open = false;

    this._usedGsubrs = usedGsubrs = {};
    this._usedSubrs = usedSubrs = {};

    var gsubrs = cff.globalSubrIndex || [];
    var gsubrsBias = this.bias(gsubrs);

    var privateDict = cff.privateDictForGlyph(this.id) || {};
    var subrs = privateDict.Subrs || [];
    var subrsBias = this.bias(subrs);

    var vstore = cff.topDict.vstore && cff.topDict.vstore.itemVariationStore;
    var vsindex = privateDict.vsindex;
    var variationProcessor = this._font._variationProcessor;

    function checkWidth() {
      if (width == null) {
        width = stack.shift() + privateDict.nominalWidthX;
      }
    }

    function parseStems() {
      if (stack.length % 2 !== 0) {
        checkWidth();
      }

      nStems += stack.length >> 1;
      return stack.length = 0;
    }

    function moveTo(x, y) {
      if (open) {
        path.closePath();
      }

      path.moveTo(x, y);
      open = true;
    }

    var parse = function parse() {
      while (stream.pos < end) {
        var op = stream.readUInt8();
        if (op < 32) {
          switch (op) {
            case 1: // hstem
            case 3: // vstem
            case 18: // hstemhm
            case 23:
              // vstemhm
              parseStems();
              break;

            case 4:
              // vmoveto
              if (stack.length > 1) {
                checkWidth();
              }

              y += stack.shift();
              moveTo(x, y);
              break;

            case 5:
              // rlineto
              while (stack.length >= 2) {
                x += stack.shift();
                y += stack.shift();
                path.lineTo(x, y);
              }
              break;

            case 6: // hlineto
            case 7:
              // vlineto
              var phase = op === 6;
              while (stack.length >= 1) {
                if (phase) {
                  x += stack.shift();
                } else {
                  y += stack.shift();
                }

                path.lineTo(x, y);
                phase = !phase;
              }
              break;

            case 8:
              // rrcurveto
              while (stack.length > 0) {
                var c1x = x + stack.shift();
                var c1y = y + stack.shift();
                var c2x = c1x + stack.shift();
                var c2y = c1y + stack.shift();
                x = c2x + stack.shift();
                y = c2y + stack.shift();
                path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
              }
              break;

            case 10:
              // callsubr
              var index = stack.pop() + subrsBias;
              var subr = subrs[index];
              if (subr) {
                usedSubrs[index] = true;
                var p = stream.pos;
                var e = end;
                stream.pos = subr.offset;
                end = subr.offset + subr.length;
                parse();
                stream.pos = p;
                end = e;
              }
              break;

            case 11:
              // return
              if (cff.version >= 2) {
                break;
              }
              return;

            case 14:
              // endchar
              if (cff.version >= 2) {
                break;
              }

              if (stack.length > 0) {
                checkWidth();
              }

              if (open) {
                path.closePath();
                open = false;
              }
              break;

            case 15:
              {
                // vsindex
                if (cff.version < 2) {
                  throw new Error('vsindex operator not supported in CFF v1');
                }

                vsindex = stack.pop();
                break;
              }

            case 16:
              {
                // blend
                if (cff.version < 2) {
                  throw new Error('blend operator not supported in CFF v1');
                }

                if (!variationProcessor) {
                  throw new Error('blend operator in non-variation font');
                }

                var blendVector = variationProcessor.getBlendVector(vstore, vsindex);
                var numBlends = stack.pop();
                var numOperands = numBlends * blendVector.length;
                var delta = stack.length - numOperands;
                var base = delta - numBlends;

                for (var i = 0; i < numBlends; i++) {
                  var sum = stack[base + i];
                  for (var j = 0; j < blendVector.length; j++) {
                    sum += blendVector[j] * stack[delta++];
                  }

                  stack[base + i] = sum;
                }

                while (numOperands--) {
                  stack.pop();
                }

                break;
              }

            case 19: // hintmask
            case 20:
              // cntrmask
              parseStems();
              stream.pos += nStems + 7 >> 3;
              break;

            case 21:
              // rmoveto
              if (stack.length > 2) {
                checkWidth();
              }

              x += stack.shift();
              y += stack.shift();
              moveTo(x, y);
              break;

            case 22:
              // hmoveto
              if (stack.length > 1) {
                checkWidth();
              }

              x += stack.shift();
              moveTo(x, y);
              break;

            case 24:
              // rcurveline
              while (stack.length >= 8) {
                var c1x = x + stack.shift();
                var c1y = y + stack.shift();
                var c2x = c1x + stack.shift();
                var c2y = c1y + stack.shift();
                x = c2x + stack.shift();
                y = c2y + stack.shift();
                path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
              }

              x += stack.shift();
              y += stack.shift();
              path.lineTo(x, y);
              break;

            case 25:
              // rlinecurve
              while (stack.length >= 8) {
                x += stack.shift();
                y += stack.shift();
                path.lineTo(x, y);
              }

              var c1x = x + stack.shift();
              var c1y = y + stack.shift();
              var c2x = c1x + stack.shift();
              var c2y = c1y + stack.shift();
              x = c2x + stack.shift();
              y = c2y + stack.shift();
              path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
              break;

            case 26:
              // vvcurveto
              if (stack.length % 2) {
                x += stack.shift();
              }

              while (stack.length >= 4) {
                c1x = x;
                c1y = y + stack.shift();
                c2x = c1x + stack.shift();
                c2y = c1y + stack.shift();
                x = c2x;
                y = c2y + stack.shift();
                path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
              }
              break;

            case 27:
              // hhcurveto
              if (stack.length % 2) {
                y += stack.shift();
              }

              while (stack.length >= 4) {
                c1x = x + stack.shift();
                c1y = y;
                c2x = c1x + stack.shift();
                c2y = c1y + stack.shift();
                x = c2x + stack.shift();
                y = c2y;
                path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
              }
              break;

            case 28:
              // shortint
              stack.push(stream.readInt16BE());
              break;

            case 29:
              // callgsubr
              index = stack.pop() + gsubrsBias;
              subr = gsubrs[index];
              if (subr) {
                usedGsubrs[index] = true;
                var p = stream.pos;
                var e = end;
                stream.pos = subr.offset;
                end = subr.offset + subr.length;
                parse();
                stream.pos = p;
                end = e;
              }
              break;

            case 30: // vhcurveto
            case 31:
              // hvcurveto
              phase = op === 31;
              while (stack.length >= 4) {
                if (phase) {
                  c1x = x + stack.shift();
                  c1y = y;
                  c2x = c1x + stack.shift();
                  c2y = c1y + stack.shift();
                  y = c2y + stack.shift();
                  x = c2x + (stack.length === 1 ? stack.shift() : 0);
                } else {
                  c1x = x;
                  c1y = y + stack.shift();
                  c2x = c1x + stack.shift();
                  c2y = c1y + stack.shift();
                  x = c2x + stack.shift();
                  y = c2y + (stack.length === 1 ? stack.shift() : 0);
                }

                path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
                phase = !phase;
              }
              break;

            case 12:
              op = stream.readUInt8();
              switch (op) {
                case 3:
                  // and
                  var a = stack.pop();
                  var b = stack.pop();
                  stack.push(a && b ? 1 : 0);
                  break;

                case 4:
                  // or
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a || b ? 1 : 0);
                  break;

                case 5:
                  // not
                  a = stack.pop();
                  stack.push(a ? 0 : 1);
                  break;

                case 9:
                  // abs
                  a = stack.pop();
                  stack.push(Math.abs(a));
                  break;

                case 10:
                  // add
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a + b);
                  break;

                case 11:
                  // sub
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a - b);
                  break;

                case 12:
                  // div
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a / b);
                  break;

                case 14:
                  // neg
                  a = stack.pop();
                  stack.push(-a);
                  break;

                case 15:
                  // eq
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a === b ? 1 : 0);
                  break;

                case 18:
                  // drop
                  stack.pop();
                  break;

                case 20:
                  // put
                  var val = stack.pop();
                  var idx = stack.pop();
                  trans[idx] = val;
                  break;

                case 21:
                  // get
                  idx = stack.pop();
                  stack.push(trans[idx] || 0);
                  break;

                case 22:
                  // ifelse
                  var s1 = stack.pop();
                  var s2 = stack.pop();
                  var v1 = stack.pop();
                  var v2 = stack.pop();
                  stack.push(v1 <= v2 ? s1 : s2);
                  break;

                case 23:
                  // random
                  stack.push(Math.random());
                  break;

                case 24:
                  // mul
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a * b);
                  break;

                case 26:
                  // sqrt
                  a = stack.pop();
                  stack.push(Math.sqrt(a));
                  break;

                case 27:
                  // dup
                  a = stack.pop();
                  stack.push(a, a);
                  break;

                case 28:
                  // exch
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(b, a);
                  break;

                case 29:
                  // index
                  idx = stack.pop();
                  if (idx < 0) {
                    idx = 0;
                  } else if (idx > stack.length - 1) {
                    idx = stack.length - 1;
                  }

                  stack.push(stack[idx]);
                  break;

                case 30:
                  // roll
                  var n = stack.pop();
                  var _j = stack.pop();

                  if (_j >= 0) {
                    while (_j > 0) {
                      var t = stack[n - 1];
                      for (var _i = n - 2; _i >= 0; _i--) {
                        stack[_i + 1] = stack[_i];
                      }

                      stack[0] = t;
                      _j--;
                    }
                  } else {
                    while (_j < 0) {
                      var t = stack[0];
                      for (var _i2 = 0; _i2 <= n; _i2++) {
                        stack[_i2] = stack[_i2 + 1];
                      }

                      stack[n - 1] = t;
                      _j++;
                    }
                  }
                  break;

                case 34:
                  // hflex
                  c1x = x + stack.shift();
                  c1y = y;
                  c2x = c1x + stack.shift();
                  c2y = c1y + stack.shift();
                  var c3x = c2x + stack.shift();
                  var c3y = c2y;
                  var c4x = c3x + stack.shift();
                  var c4y = c3y;
                  var c5x = c4x + stack.shift();
                  var c5y = c4y;
                  var c6x = c5x + stack.shift();
                  var c6y = c5y;
                  x = c6x;
                  y = c6y;

                  path.bezierCurveTo(c1x, c1y, c2x, c2y, c3x, c3y);
                  path.bezierCurveTo(c4x, c4y, c5x, c5y, c6x, c6y);
                  break;

                case 35:
                  // flex
                  var pts = [];

                  for (var _i3 = 0; _i3 <= 5; _i3++) {
                    x += stack.shift();
                    y += stack.shift();
                    pts.push(x, y);
                  }

                  path.bezierCurveTo.apply(path, pts.slice(0, 6));
                  path.bezierCurveTo.apply(path, pts.slice(6));
                  stack.shift(); // fd
                  break;

                case 36:
                  // hflex1
                  c1x = x + stack.shift();
                  c1y = y + stack.shift();
                  c2x = c1x + stack.shift();
                  c2y = c1y + stack.shift();
                  c3x = c2x + stack.shift();
                  c3y = c2y;
                  c4x = c3x + stack.shift();
                  c4y = c3y;
                  c5x = c4x + stack.shift();
                  c5y = c4y + stack.shift();
                  c6x = c5x + stack.shift();
                  c6y = c5y;
                  x = c6x;
                  y = c6y;

                  path.bezierCurveTo(c1x, c1y, c2x, c2y, c3x, c3y);
                  path.bezierCurveTo(c4x, c4y, c5x, c5y, c6x, c6y);
                  break;

                case 37:
                  // flex1
                  var startx = x;
                  var starty = y;

                  pts = [];
                  for (var _i4 = 0; _i4 <= 4; _i4++) {
                    x += stack.shift();
                    y += stack.shift();
                    pts.push(x, y);
                  }

                  if (Math.abs(x - startx) > Math.abs(y - starty)) {
                    // horizontal
                    x += stack.shift();
                    y = starty;
                  } else {
                    x = startx;
                    y += stack.shift();
                  }

                  pts.push(x, y);
                  path.bezierCurveTo.apply(path, pts.slice(0, 6));
                  path.bezierCurveTo.apply(path, pts.slice(6));
                  break;

                default:
                  throw new Error('Unknown op: 12 ' + op);
              }
              break;

            default:
              throw new Error('Unknown op: ' + op);
          }
        } else if (op < 247) {
          stack.push(op - 139);
        } else if (op < 251) {
          var b1 = stream.readUInt8();
          stack.push((op - 247) * 256 + b1 + 108);
        } else if (op < 255) {
          var b1 = stream.readUInt8();
          stack.push(-(op - 251) * 256 - b1 - 108);
        } else {
          stack.push(stream.readInt32BE() / 65536);
        }
      }
    };

    parse();

    if (open) {
      path.closePath();
    }

    return path;
  };

  return CFFGlyph;
}(Glyph);

var SBIXImage = new r.Struct({
  originX: r.uint16,
  originY: r.uint16,
  type: new r.String(4),
  data: new r.Buffer(function (t) {
    return t.parent.buflen - t._currentOffset;
  })
});

/**
 * Represents a color (e.g. emoji) glyph in Apple's SBIX format.
 */

var SBIXGlyph = function (_TTFGlyph) {
  _inherits(SBIXGlyph, _TTFGlyph);

  function SBIXGlyph() {
    _classCallCheck(this, SBIXGlyph);

    return _possibleConstructorReturn(this, _TTFGlyph.apply(this, arguments));
  }

  /**
   * Returns an object representing a glyph image at the given point size.
   * The object has a data property with a Buffer containing the actual image data,
   * along with the image type, and origin.
   *
   * @param {number} size
   * @return {object}
   */
  SBIXGlyph.prototype.getImageForSize = function getImageForSize(size) {
    for (var i = 0; i < this._font.sbix.imageTables.length; i++) {
      var table = this._font.sbix.imageTables[i];
      if (table.ppem >= size) {
        break;
      }
    }

    var offsets = table.imageOffsets;
    var start = offsets[this.id];
    var end = offsets[this.id + 1];

    if (start === end) {
      return null;
    }

    this._font.stream.pos = start;
    return SBIXImage.decode(this._font.stream, { buflen: end - start });
  };

  SBIXGlyph.prototype.render = function render(ctx, size) {
    var img = this.getImageForSize(size);
    if (img != null) {
      var scale = size / this._font.unitsPerEm;
      ctx.image(img.data, { height: size, x: img.originX, y: (this.bbox.minY - img.originY) * scale });
    }

    if (this._font.sbix.flags.renderOutlines) {
      _TTFGlyph.prototype.render.call(this, ctx, size);
    }
  };

  return SBIXGlyph;
}(TTFGlyph);

var COLRLayer = function COLRLayer(glyph, color) {
  _classCallCheck(this, COLRLayer);

  this.glyph = glyph;
  this.color = color;
};

/**
 * Represents a color (e.g. emoji) glyph in Microsoft's COLR format.
 * Each glyph in this format contain a list of colored layers, each
 * of which  is another vector glyph.
 */


var COLRGlyph = function (_Glyph) {
  _inherits(COLRGlyph, _Glyph);

  function COLRGlyph() {
    _classCallCheck(this, COLRGlyph);

    return _possibleConstructorReturn(this, _Glyph.apply(this, arguments));
  }

  COLRGlyph.prototype._getBBox = function _getBBox() {
    var bbox = new BBox();
    for (var i = 0; i < this.layers.length; i++) {
      var layer = this.layers[i];
      var b = layer.glyph.bbox;
      bbox.addPoint(b.minX, b.minY);
      bbox.addPoint(b.maxX, b.maxY);
    }

    return bbox;
  };

  /**
   * Returns an array of objects containing the glyph and color for
   * each layer in the composite color glyph.
   * @type {object[]}
   */


  COLRGlyph.prototype.render = function render(ctx, size) {
    for (var _iterator = this.layers, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var _ref2 = _ref,
          glyph = _ref2.glyph,
          color = _ref2.color;

      ctx.fillColor([color.red, color.green, color.blue], color.alpha / 255 * 100);
      glyph.render(ctx, size);
    }

    return;
  };

  _createClass(COLRGlyph, [{
    key: 'layers',
    get: function get() {
      var cpal = this._font.CPAL;
      var colr = this._font.COLR;
      var low = 0;
      var high = colr.baseGlyphRecord.length - 1;

      while (low <= high) {
        var mid = low + high >> 1;
        var rec = colr.baseGlyphRecord[mid];

        if (this.id < rec.gid) {
          high = mid - 1;
        } else if (this.id > rec.gid) {
          low = mid + 1;
        } else {
          var baseLayer = rec;
          break;
        }
      }

      // if base glyph not found in COLR table,
      // default to normal glyph from glyf or CFF
      if (baseLayer == null) {
        var g = this._font._getBaseGlyph(this.id);
        var color = {
          red: 0,
          green: 0,
          blue: 0,
          alpha: 255
        };

        return [new COLRLayer(g, color)];
      }

      // otherwise, return an array of all the layers
      var layers = [];
      for (var i = baseLayer.firstLayerIndex; i < baseLayer.firstLayerIndex + baseLayer.numLayers; i++) {
        var rec = colr.layerRecords[i];
        var color = cpal.colorRecords[rec.paletteIndex];
        var g = this._font._getBaseGlyph(rec.gid);
        layers.push(new COLRLayer(g, color));
      }

      return layers;
    }
  }]);

  return COLRGlyph;
}(Glyph);

var TUPLES_SHARE_POINT_NUMBERS = 0x8000;
var TUPLE_COUNT_MASK = 0x0fff;
var EMBEDDED_TUPLE_COORD = 0x8000;
var INTERMEDIATE_TUPLE = 0x4000;
var PRIVATE_POINT_NUMBERS = 0x2000;
var TUPLE_INDEX_MASK = 0x0fff;
var POINTS_ARE_WORDS = 0x80;
var POINT_RUN_COUNT_MASK = 0x7f;
var DELTAS_ARE_ZERO = 0x80;
var DELTAS_ARE_WORDS = 0x40;
var DELTA_RUN_COUNT_MASK = 0x3f;

/**
 * This class is transforms TrueType glyphs according to the data from
 * the Apple Advanced Typography variation tables (fvar, gvar, and avar).
 * These tables allow infinite adjustments to glyph weight, width, slant,
 * and optical size without the designer needing to specify every exact style.
 *
 * Apple's documentation for these tables is not great, so thanks to the
 * Freetype project for figuring much of this out.
 *
 * @private
 */

var GlyphVariationProcessor = function () {
  function GlyphVariationProcessor(font, coords) {
    _classCallCheck(this, GlyphVariationProcessor);

    this.font = font;
    this.normalizedCoords = this.normalizeCoords(coords);
    this.blendVectors = new _Map();
  }

  GlyphVariationProcessor.prototype.normalizeCoords = function normalizeCoords(coords) {
    // the default mapping is linear along each axis, in two segments:
    // from the minValue to defaultValue, and from defaultValue to maxValue.
    var normalized = [];
    for (var i = 0; i < this.font.fvar.axis.length; i++) {
      var axis = this.font.fvar.axis[i];
      if (coords[i] < axis.defaultValue) {
        normalized.push((coords[i] - axis.defaultValue + _Number$EPSILON) / (axis.defaultValue - axis.minValue + _Number$EPSILON));
      } else {
        normalized.push((coords[i] - axis.defaultValue + _Number$EPSILON) / (axis.maxValue - axis.defaultValue + _Number$EPSILON));
      }
    }

    // if there is an avar table, the normalized value is calculated
    // by interpolating between the two nearest mapped values.
    if (this.font.avar) {
      for (var i = 0; i < this.font.avar.segment.length; i++) {
        var segment = this.font.avar.segment[i];
        for (var j = 0; j < segment.correspondence.length; j++) {
          var pair = segment.correspondence[j];
          if (j >= 1 && normalized[i] < pair.fromCoord) {
            var prev = segment.correspondence[j - 1];
            normalized[i] = ((normalized[i] - prev.fromCoord) * (pair.toCoord - prev.toCoord) + _Number$EPSILON) / (pair.fromCoord - prev.fromCoord + _Number$EPSILON) + prev.toCoord;

            break;
          }
        }
      }
    }

    return normalized;
  };

  GlyphVariationProcessor.prototype.transformPoints = function transformPoints(gid, glyphPoints) {
    if (!this.font.fvar || !this.font.gvar) {
      return;
    }

    var gvar = this.font.gvar;

    if (gid >= gvar.glyphCount) {
      return;
    }

    var offset = gvar.offsets[gid];
    if (offset === gvar.offsets[gid + 1]) {
      return;
    }

    // Read the gvar data for this glyph
    var stream = this.font.stream;

    stream.pos = offset;
    if (stream.pos >= stream.length) {
      return;
    }

    var tupleCount = stream.readUInt16BE();
    var offsetToData = offset + stream.readUInt16BE();

    if (tupleCount & TUPLES_SHARE_POINT_NUMBERS) {
      var here = stream.pos;
      stream.pos = offsetToData;
      var sharedPoints = this.decodePoints();
      offsetToData = stream.pos;
      stream.pos = here;
    }

    var origPoints = glyphPoints.map(function (pt) {
      return pt.copy();
    });

    tupleCount &= TUPLE_COUNT_MASK;
    for (var i = 0; i < tupleCount; i++) {
      var tupleDataSize = stream.readUInt16BE();
      var tupleIndex = stream.readUInt16BE();

      if (tupleIndex & EMBEDDED_TUPLE_COORD) {
        var tupleCoords = [];
        for (var a = 0; a < gvar.axisCount; a++) {
          tupleCoords.push(stream.readInt16BE() / 16384);
        }
      } else {
        if ((tupleIndex & TUPLE_INDEX_MASK) >= gvar.globalCoordCount) {
          throw new Error('Invalid gvar table');
        }

        var tupleCoords = gvar.globalCoords[tupleIndex & TUPLE_INDEX_MASK];
      }

      if (tupleIndex & INTERMEDIATE_TUPLE) {
        var startCoords = [];
        for (var _a = 0; _a < gvar.axisCount; _a++) {
          startCoords.push(stream.readInt16BE() / 16384);
        }

        var endCoords = [];
        for (var _a2 = 0; _a2 < gvar.axisCount; _a2++) {
          endCoords.push(stream.readInt16BE() / 16384);
        }
      }

      // Get the factor at which to apply this tuple
      var factor = this.tupleFactor(tupleIndex, tupleCoords, startCoords, endCoords);
      if (factor === 0) {
        offsetToData += tupleDataSize;
        continue;
      }

      var here = stream.pos;
      stream.pos = offsetToData;

      if (tupleIndex & PRIVATE_POINT_NUMBERS) {
        var points = this.decodePoints();
      } else {
        var points = sharedPoints;
      }

      // points.length = 0 means there are deltas for all points
      var nPoints = points.length === 0 ? glyphPoints.length : points.length;
      var xDeltas = this.decodeDeltas(nPoints);
      var yDeltas = this.decodeDeltas(nPoints);

      if (points.length === 0) {
        // all points
        for (var _i = 0; _i < glyphPoints.length; _i++) {
          var point = glyphPoints[_i];
          point.x += Math.round(xDeltas[_i] * factor);
          point.y += Math.round(yDeltas[_i] * factor);
        }
      } else {
        var outPoints = origPoints.map(function (pt) {
          return pt.copy();
        });
        var hasDelta = glyphPoints.map(function () {
          return false;
        });

        for (var _i2 = 0; _i2 < points.length; _i2++) {
          var idx = points[_i2];
          if (idx < glyphPoints.length) {
            var _point = outPoints[idx];
            hasDelta[idx] = true;

            _point.x += Math.round(xDeltas[_i2] * factor);
            _point.y += Math.round(yDeltas[_i2] * factor);
          }
        }

        this.interpolateMissingDeltas(outPoints, origPoints, hasDelta);

        for (var _i3 = 0; _i3 < glyphPoints.length; _i3++) {
          var deltaX = outPoints[_i3].x - origPoints[_i3].x;
          var deltaY = outPoints[_i3].y - origPoints[_i3].y;

          glyphPoints[_i3].x += deltaX;
          glyphPoints[_i3].y += deltaY;
        }
      }

      offsetToData += tupleDataSize;
      stream.pos = here;
    }
  };

  GlyphVariationProcessor.prototype.decodePoints = function decodePoints() {
    var stream = this.font.stream;
    var count = stream.readUInt8();

    if (count & POINTS_ARE_WORDS) {
      count = (count & POINT_RUN_COUNT_MASK) << 8 | stream.readUInt8();
    }

    var points = new Uint16Array(count);
    var i = 0;
    var point = 0;
    while (i < count) {
      var run = stream.readUInt8();
      var runCount = (run & POINT_RUN_COUNT_MASK) + 1;
      var fn = run & POINTS_ARE_WORDS ? stream.readUInt16 : stream.readUInt8;

      for (var j = 0; j < runCount && i < count; j++) {
        point += fn.call(stream);
        points[i++] = point;
      }
    }

    return points;
  };

  GlyphVariationProcessor.prototype.decodeDeltas = function decodeDeltas(count) {
    var stream = this.font.stream;
    var i = 0;
    var deltas = new Int16Array(count);

    while (i < count) {
      var run = stream.readUInt8();
      var runCount = (run & DELTA_RUN_COUNT_MASK) + 1;

      if (run & DELTAS_ARE_ZERO) {
        i += runCount;
      } else {
        var fn = run & DELTAS_ARE_WORDS ? stream.readInt16BE : stream.readInt8;
        for (var j = 0; j < runCount && i < count; j++) {
          deltas[i++] = fn.call(stream);
        }
      }
    }

    return deltas;
  };

  GlyphVariationProcessor.prototype.tupleFactor = function tupleFactor(tupleIndex, tupleCoords, startCoords, endCoords) {
    var normalized = this.normalizedCoords;
    var gvar = this.font.gvar;

    var factor = 1;

    for (var i = 0; i < gvar.axisCount; i++) {
      if (tupleCoords[i] === 0) {
        continue;
      }

      if (normalized[i] === 0) {
        return 0;
      }

      if ((tupleIndex & INTERMEDIATE_TUPLE) === 0) {
        if (normalized[i] < Math.min(0, tupleCoords[i]) || normalized[i] > Math.max(0, tupleCoords[i])) {
          return 0;
        }

        factor = (factor * normalized[i] + _Number$EPSILON) / (tupleCoords[i] + _Number$EPSILON);
      } else {
        if (normalized[i] < startCoords[i] || normalized[i] > endCoords[i]) {
          return 0;
        } else if (normalized[i] < tupleCoords[i]) {
          factor = factor * (normalized[i] - startCoords[i] + _Number$EPSILON) / (tupleCoords[i] - startCoords[i] + _Number$EPSILON);
        } else {
          factor = factor * (endCoords[i] - normalized[i] + _Number$EPSILON) / (endCoords[i] - tupleCoords[i] + _Number$EPSILON);
        }
      }
    }

    return factor;
  };

  // Interpolates points without delta values.
  // Needed for the Ø and Q glyphs in Skia.
  // Algorithm from Freetype.


  GlyphVariationProcessor.prototype.interpolateMissingDeltas = function interpolateMissingDeltas(points, inPoints, hasDelta) {
    if (points.length === 0) {
      return;
    }

    var point = 0;
    while (point < points.length) {
      var firstPoint = point;

      // find the end point of the contour
      var endPoint = point;
      var pt = points[endPoint];
      while (!pt.endContour) {
        pt = points[++endPoint];
      }

      // find the first point that has a delta
      while (point <= endPoint && !hasDelta[point]) {
        point++;
      }

      if (point > endPoint) {
        continue;
      }

      var firstDelta = point;
      var curDelta = point;
      point++;

      while (point <= endPoint) {
        // find the next point with a delta, and interpolate intermediate points
        if (hasDelta[point]) {
          this.deltaInterpolate(curDelta + 1, point - 1, curDelta, point, inPoints, points);
          curDelta = point;
        }

        point++;
      }

      // shift contour if we only have a single delta
      if (curDelta === firstDelta) {
        this.deltaShift(firstPoint, endPoint, curDelta, inPoints, points);
      } else {
        // otherwise, handle the remaining points at the end and beginning of the contour
        this.deltaInterpolate(curDelta + 1, endPoint, curDelta, firstDelta, inPoints, points);

        if (firstDelta > 0) {
          this.deltaInterpolate(firstPoint, firstDelta - 1, curDelta, firstDelta, inPoints, points);
        }
      }

      point = endPoint + 1;
    }
  };

  GlyphVariationProcessor.prototype.deltaInterpolate = function deltaInterpolate(p1, p2, ref1, ref2, inPoints, outPoints) {
    if (p1 > p2) {
      return;
    }

    var iterable = ['x', 'y'];
    for (var i = 0; i < iterable.length; i++) {
      var k = iterable[i];
      if (inPoints[ref1][k] > inPoints[ref2][k]) {
        var p = ref1;
        ref1 = ref2;
        ref2 = p;
      }

      var in1 = inPoints[ref1][k];
      var in2 = inPoints[ref2][k];
      var out1 = outPoints[ref1][k];
      var out2 = outPoints[ref2][k];

      // If the reference points have the same coordinate but different
      // delta, inferred delta is zero.  Otherwise interpolate.
      if (in1 !== in2 || out1 === out2) {
        var scale = in1 === in2 ? 0 : (out2 - out1) / (in2 - in1);

        for (var _p = p1; _p <= p2; _p++) {
          var out = inPoints[_p][k];

          if (out <= in1) {
            out += out1 - in1;
          } else if (out >= in2) {
            out += out2 - in2;
          } else {
            out = out1 + (out - in1) * scale;
          }

          outPoints[_p][k] = out;
        }
      }
    }
  };

  GlyphVariationProcessor.prototype.deltaShift = function deltaShift(p1, p2, ref, inPoints, outPoints) {
    var deltaX = outPoints[ref].x - inPoints[ref].x;
    var deltaY = outPoints[ref].y - inPoints[ref].y;

    if (deltaX === 0 && deltaY === 0) {
      return;
    }

    for (var p = p1; p <= p2; p++) {
      if (p !== ref) {
        outPoints[p].x += deltaX;
        outPoints[p].y += deltaY;
      }
    }
  };

  GlyphVariationProcessor.prototype.getAdvanceAdjustment = function getAdvanceAdjustment(gid, table) {
    var outerIndex = void 0,
        innerIndex = void 0;

    if (table.advanceWidthMapping) {
      var idx = gid;
      if (idx >= table.advanceWidthMapping.mapCount) {
        idx = table.advanceWidthMapping.mapCount - 1;
      }

      var entryFormat = table.advanceWidthMapping.entryFormat;
      var _table$advanceWidthMa = table.advanceWidthMapping.mapData[idx];
      outerIndex = _table$advanceWidthMa.outerIndex;
      innerIndex = _table$advanceWidthMa.innerIndex;
    } else {
      outerIndex = 0;
      innerIndex = gid;
    }

    return this.getDelta(table.itemVariationStore, outerIndex, innerIndex);
  };

  // See pseudo code from `Font Variations Overview'
  // in the OpenType specification.


  GlyphVariationProcessor.prototype.getDelta = function getDelta(itemStore, outerIndex, innerIndex) {
    if (outerIndex >= itemStore.itemVariationData.length) {
      return 0;
    }

    var varData = itemStore.itemVariationData[outerIndex];
    if (innerIndex >= varData.deltaSets.length) {
      return 0;
    }

    var deltaSet = varData.deltaSets[innerIndex];
    var blendVector = this.getBlendVector(itemStore, outerIndex);
    var netAdjustment = 0;

    for (var master = 0; master < varData.regionIndexCount; master++) {
      netAdjustment += deltaSet.deltas[master] * blendVector[master];
    }

    return netAdjustment;
  };

  GlyphVariationProcessor.prototype.getBlendVector = function getBlendVector(itemStore, outerIndex) {
    var varData = itemStore.itemVariationData[outerIndex];
    if (this.blendVectors.has(varData)) {
      return this.blendVectors.get(varData);
    }

    var normalizedCoords = this.normalizedCoords;
    var blendVector = [];

    // outer loop steps through master designs to be blended
    for (var master = 0; master < varData.regionIndexCount; master++) {
      var scalar = 1;
      var regionIndex = varData.regionIndexes[master];
      var axes = itemStore.variationRegionList.variationRegions[regionIndex];

      // inner loop steps through axes in this region
      for (var j = 0; j < axes.length; j++) {
        var axis = axes[j];
        var axisScalar = void 0;

        // compute the scalar contribution of this axis
        // ignore invalid ranges
        if (axis.startCoord > axis.peakCoord || axis.peakCoord > axis.endCoord) {
          axisScalar = 1;
        } else if (axis.startCoord < 0 && axis.endCoord > 0 && axis.peakCoord !== 0) {
          axisScalar = 1;

          // peak of 0 means ignore this axis
        } else if (axis.peakCoord === 0) {
          axisScalar = 1;

          // ignore this region if coords are out of range
        } else if (normalizedCoords[j] < axis.startCoord || normalizedCoords[j] > axis.endCoord) {
          axisScalar = 0;

          // calculate a proportional factor
        } else {
          if (normalizedCoords[j] === axis.peakCoord) {
            axisScalar = 1;
          } else if (normalizedCoords[j] < axis.peakCoord) {
            axisScalar = (normalizedCoords[j] - axis.startCoord + _Number$EPSILON) / (axis.peakCoord - axis.startCoord + _Number$EPSILON);
          } else {
            axisScalar = (axis.endCoord - normalizedCoords[j] + _Number$EPSILON) / (axis.endCoord - axis.peakCoord + _Number$EPSILON);
          }
        }

        // take product of all the axis scalars
        scalar *= axisScalar;
      }

      blendVector[master] = scalar;
    }

    this.blendVectors.set(varData, blendVector);
    return blendVector;
  };

  return GlyphVariationProcessor;
}();

var resolved = _Promise.resolve();

var Subset = function () {
  function Subset(font) {
    _classCallCheck(this, Subset);

    this.font = font;
    this.glyphs = [];
    this.mapping = {};

    // always include the missing glyph
    this.includeGlyph(0);
  }

  Subset.prototype.includeGlyph = function includeGlyph(glyph) {
    if ((typeof glyph === 'undefined' ? 'undefined' : _typeof(glyph)) === 'object') {
      glyph = glyph.id;
    }

    if (this.mapping[glyph] == null) {
      this.glyphs.push(glyph);
      this.mapping[glyph] = this.glyphs.length - 1;
    }

    return this.mapping[glyph];
  };

  Subset.prototype.encodeStream = function encodeStream() {
    var _this = this;

    var s = new r.EncodeStream();

    resolved.then(function () {
      _this.encode(s);
      return s.end();
    });

    return s;
  };

  return Subset;
}();

// Flags for simple glyphs
var ON_CURVE$1 = 1 << 0;
var X_SHORT_VECTOR$1 = 1 << 1;
var Y_SHORT_VECTOR$1 = 1 << 2;
var REPEAT$1 = 1 << 3;
var SAME_X$1 = 1 << 4;
var SAME_Y$1 = 1 << 5;

var Point$1 = function () {
  function Point() {
    _classCallCheck(this, Point);
  }

  Point.size = function size(val) {
    return val >= 0 && val <= 255 ? 1 : 2;
  };

  Point.encode = function encode(stream, value) {
    if (value >= 0 && value <= 255) {
      stream.writeUInt8(value);
    } else {
      stream.writeInt16BE(value);
    }
  };

  return Point;
}();

var Glyf = new r.Struct({
  numberOfContours: r.int16, // if negative, this is a composite glyph
  xMin: r.int16,
  yMin: r.int16,
  xMax: r.int16,
  yMax: r.int16,
  endPtsOfContours: new r.Array(r.uint16, 'numberOfContours'),
  instructions: new r.Array(r.uint8, r.uint16),
  flags: new r.Array(r.uint8, 0),
  xPoints: new r.Array(Point$1, 0),
  yPoints: new r.Array(Point$1, 0)
});

/**
 * Encodes TrueType glyph outlines
 */

var TTFGlyphEncoder = function () {
  function TTFGlyphEncoder() {
    _classCallCheck(this, TTFGlyphEncoder);
  }

  TTFGlyphEncoder.prototype.encodeSimple = function encodeSimple(path) {
    var instructions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

    var endPtsOfContours = [];
    var xPoints = [];
    var yPoints = [];
    var flags = [];
    var same = 0;
    var lastX = 0,
        lastY = 0,
        lastFlag = 0;
    var pointCount = 0;

    for (var i = 0; i < path.commands.length; i++) {
      var c = path.commands[i];

      for (var j = 0; j < c.args.length; j += 2) {
        var x = c.args[j];
        var y = c.args[j + 1];
        var flag = 0;

        // If the ending point of a quadratic curve is the midpoint
        // between the control point and the control point of the next
        // quadratic curve, we can omit the ending point.
        if (c.command === 'quadraticCurveTo' && j === 2) {
          var next = path.commands[i + 1];
          if (next && next.command === 'quadraticCurveTo') {
            var midX = (lastX + next.args[0]) / 2;
            var midY = (lastY + next.args[1]) / 2;

            if (x === midX && y === midY) {
              continue;
            }
          }
        }

        // All points except control points are on curve.
        if (!(c.command === 'quadraticCurveTo' && j === 0)) {
          flag |= ON_CURVE$1;
        }

        flag = this._encodePoint(x, lastX, xPoints, flag, X_SHORT_VECTOR$1, SAME_X$1);
        flag = this._encodePoint(y, lastY, yPoints, flag, Y_SHORT_VECTOR$1, SAME_Y$1);

        if (flag === lastFlag && same < 255) {
          flags[flags.length - 1] |= REPEAT$1;
          same++;
        } else {
          if (same > 0) {
            flags.push(same);
            same = 0;
          }

          flags.push(flag);
          lastFlag = flag;
        }

        lastX = x;
        lastY = y;
        pointCount++;
      }

      if (c.command === 'closePath') {
        endPtsOfContours.push(pointCount - 1);
      }
    }

    // Close the path if the last command didn't already
    if (path.commands.length > 1 && path.commands[path.commands.length - 1].command !== 'closePath') {
      endPtsOfContours.push(pointCount - 1);
    }

    var bbox = path.bbox;
    var glyf = {
      numberOfContours: endPtsOfContours.length,
      xMin: bbox.minX,
      yMin: bbox.minY,
      xMax: bbox.maxX,
      yMax: bbox.maxY,
      endPtsOfContours: endPtsOfContours,
      instructions: instructions,
      flags: flags,
      xPoints: xPoints,
      yPoints: yPoints
    };

    var size = Glyf.size(glyf);
    var tail = 4 - size % 4;

    var stream = new r.EncodeStream(size + tail);
    Glyf.encode(stream, glyf);

    // Align to 4-byte length
    if (tail !== 0) {
      stream.fill(0, tail);
    }

    return stream.buffer;
  };

  TTFGlyphEncoder.prototype._encodePoint = function _encodePoint(value, last, points, flag, shortFlag, sameFlag) {
    var diff = value - last;

    if (value === last) {
      flag |= sameFlag;
    } else {
      if (-255 <= diff && diff <= 255) {
        flag |= shortFlag;
        if (diff < 0) {
          diff = -diff;
        } else {
          flag |= sameFlag;
        }
      }

      points.push(diff);
    }

    return flag;
  };

  return TTFGlyphEncoder;
}();

var TTFSubset = function (_Subset) {
  _inherits(TTFSubset, _Subset);

  function TTFSubset(font) {
    _classCallCheck(this, TTFSubset);

    var _this = _possibleConstructorReturn(this, _Subset.call(this, font));

    _this.glyphEncoder = new TTFGlyphEncoder();
    return _this;
  }

  TTFSubset.prototype._addGlyph = function _addGlyph(gid) {
    var glyph = this.font.getGlyph(gid);
    var glyf = glyph._decode();

    // get the offset to the glyph from the loca table
    var curOffset = this.font.loca.offsets[gid];
    var nextOffset = this.font.loca.offsets[gid + 1];

    var stream = this.font._getTableStream('glyf');
    stream.pos += curOffset;

    var buffer = stream.readBuffer(nextOffset - curOffset);

    // if it is a compound glyph, include its components
    if (glyf && glyf.numberOfContours < 0) {
      buffer = new Buffer(buffer);
      for (var _iterator = glyf.components, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
        var _ref;

        if (_isArray) {
          if (_i >= _iterator.length) break;
          _ref = _iterator[_i++];
        } else {
          _i = _iterator.next();
          if (_i.done) break;
          _ref = _i.value;
        }

        var component = _ref;

        gid = this.includeGlyph(component.glyphID);
        buffer.writeUInt16BE(gid, component.pos);
      }
    } else if (glyf && this.font._variationProcessor) {
      // If this is a TrueType variation glyph, re-encode the path
      buffer = this.glyphEncoder.encodeSimple(glyph.path, glyf.instructions);
    }

    this.glyf.push(buffer);
    this.loca.offsets.push(this.offset);

    this.hmtx.metrics.push({
      advance: glyph.advanceWidth,
      bearing: glyph._getMetrics().leftBearing
    });

    this.offset += buffer.length;
    return this.glyf.length - 1;
  };

  TTFSubset.prototype.encode = function encode(stream) {
    // tables required by PDF spec:
    //   head, hhea, loca, maxp, cvt , prep, glyf, hmtx, fpgm
    //
    // additional tables required for standalone fonts:
    //   name, cmap, OS/2, post

    this.glyf = [];
    this.offset = 0;
    this.loca = {
      offsets: [],
      version: this.font.loca.version
    };

    this.hmtx = {
      metrics: [],
      bearings: []
    };

    // include all the glyphs
    // not using a for loop because we need to support adding more
    // glyphs to the array as we go, and CoffeeScript caches the length.
    var i = 0;
    while (i < this.glyphs.length) {
      this._addGlyph(this.glyphs[i++]);
    }

    var maxp = cloneDeep(this.font.maxp);
    maxp.numGlyphs = this.glyf.length;

    this.loca.offsets.push(this.offset);

    var head = cloneDeep(this.font.head);
    head.indexToLocFormat = this.loca.version;

    var hhea = cloneDeep(this.font.hhea);
    hhea.numberOfMetrics = this.hmtx.metrics.length;

    // map = []
    // for index in [0...256]
    //     if index < @numGlyphs
    //         map[index] = index
    //     else
    //         map[index] = 0
    //
    // cmapTable =
    //     version: 0
    //     length: 262
    //     language: 0
    //     codeMap: map
    //
    // cmap =
    //     version: 0
    //     numSubtables: 1
    //     tables: [
    //         platformID: 1
    //         encodingID: 0
    //         table: cmapTable
    //     ]

    // TODO: subset prep, cvt, fpgm?
    Directory.encode(stream, {
      tables: {
        head: head,
        hhea: hhea,
        loca: this.loca,
        maxp: maxp,
        'cvt ': this.font['cvt '],
        prep: this.font.prep,
        glyf: this.glyf,
        hmtx: this.hmtx,
        fpgm: this.font.fpgm

        // name: clone @font.name
        // 'OS/2': clone @font['OS/2']
        // post: clone @font.post
        // cmap: cmap
      }
    });
  };

  return TTFSubset;
}(Subset);

var CFFSubset = function (_Subset) {
  _inherits(CFFSubset, _Subset);

  function CFFSubset(font) {
    _classCallCheck(this, CFFSubset);

    var _this = _possibleConstructorReturn(this, _Subset.call(this, font));

    _this.cff = _this.font['CFF '];
    if (!_this.cff) {
      throw new Error('Not a CFF Font');
    }
    return _this;
  }

  CFFSubset.prototype.subsetCharstrings = function subsetCharstrings() {
    this.charstrings = [];
    var gsubrs = {};

    for (var _iterator = this.glyphs, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var gid = _ref;

      this.charstrings.push(this.cff.getCharString(gid));

      var glyph = this.font.getGlyph(gid);
      var path = glyph.path; // this causes the glyph to be parsed

      for (var subr in glyph._usedGsubrs) {
        gsubrs[subr] = true;
      }
    }

    this.gsubrs = this.subsetSubrs(this.cff.globalSubrIndex, gsubrs);
  };

  CFFSubset.prototype.subsetSubrs = function subsetSubrs(subrs, used) {
    var res = [];
    for (var i = 0; i < subrs.length; i++) {
      var subr = subrs[i];
      if (used[i]) {
        this.cff.stream.pos = subr.offset;
        res.push(this.cff.stream.readBuffer(subr.length));
      } else {
        res.push(new Buffer([11])); // return
      }
    }

    return res;
  };

  CFFSubset.prototype.subsetFontdict = function subsetFontdict(topDict) {
    topDict.FDArray = [];
    topDict.FDSelect = {
      version: 0,
      fds: []
    };

    var used_fds = {};
    var used_subrs = [];
    for (var _iterator2 = this.glyphs, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
      var _ref2;

      if (_isArray2) {
        if (_i2 >= _iterator2.length) break;
        _ref2 = _iterator2[_i2++];
      } else {
        _i2 = _iterator2.next();
        if (_i2.done) break;
        _ref2 = _i2.value;
      }

      var gid = _ref2;

      var fd = this.cff.fdForGlyph(gid);
      if (fd == null) {
        continue;
      }

      if (!used_fds[fd]) {
        topDict.FDArray.push(_Object$assign({}, this.cff.topDict.FDArray[fd]));
        used_subrs.push({});
      }

      used_fds[fd] = true;
      topDict.FDSelect.fds.push(topDict.FDArray.length - 1);

      var glyph = this.font.getGlyph(gid);
      var path = glyph.path; // this causes the glyph to be parsed
      for (var subr in glyph._usedSubrs) {
        used_subrs[used_subrs.length - 1][subr] = true;
      }
    }

    for (var i = 0; i < topDict.FDArray.length; i++) {
      var dict = topDict.FDArray[i];
      delete dict.FontName;
      if (dict.Private && dict.Private.Subrs) {
        dict.Private = _Object$assign({}, dict.Private);
        dict.Private.Subrs = this.subsetSubrs(dict.Private.Subrs, used_subrs[i]);
      }
    }

    return;
  };

  CFFSubset.prototype.createCIDFontdict = function createCIDFontdict(topDict) {
    var used_subrs = {};
    for (var _iterator3 = this.glyphs, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
      var _ref3;

      if (_isArray3) {
        if (_i3 >= _iterator3.length) break;
        _ref3 = _iterator3[_i3++];
      } else {
        _i3 = _iterator3.next();
        if (_i3.done) break;
        _ref3 = _i3.value;
      }

      var gid = _ref3;

      var glyph = this.font.getGlyph(gid);
      var path = glyph.path; // this causes the glyph to be parsed

      for (var subr in glyph._usedSubrs) {
        used_subrs[subr] = true;
      }
    }

    var privateDict = _Object$assign({}, this.cff.topDict.Private);
    if (this.cff.topDict.Private && this.cff.topDict.Private.Subrs) {
      privateDict.Subrs = this.subsetSubrs(this.cff.topDict.Private.Subrs, used_subrs);
    }

    topDict.FDArray = [{ Private: privateDict }];
    return topDict.FDSelect = {
      version: 3,
      nRanges: 1,
      ranges: [{ first: 0, fd: 0 }],
      sentinel: this.charstrings.length
    };
  };

  CFFSubset.prototype.addString = function addString(string) {
    if (!string) {
      return null;
    }

    if (!this.strings) {
      this.strings = [];
    }

    this.strings.push(string);
    return standardStrings.length + this.strings.length - 1;
  };

  CFFSubset.prototype.encode = function encode(stream) {
    this.subsetCharstrings();

    var charset = {
      version: this.charstrings.length > 255 ? 2 : 1,
      ranges: [{ first: 1, nLeft: this.charstrings.length - 2 }]
    };

    var topDict = _Object$assign({}, this.cff.topDict);
    topDict.Private = null;
    topDict.charset = charset;
    topDict.Encoding = null;
    topDict.CharStrings = this.charstrings;

    var _arr = ['version', 'Notice', 'Copyright', 'FullName', 'FamilyName', 'Weight', 'PostScript', 'BaseFontName', 'FontName'];
    for (var _i4 = 0; _i4 < _arr.length; _i4++) {
      var key = _arr[_i4];
      topDict[key] = this.addString(this.cff.string(topDict[key]));
    }

    topDict.ROS = [this.addString('Adobe'), this.addString('Identity'), 0];
    topDict.CIDCount = this.charstrings.length;

    if (this.cff.isCIDFont) {
      this.subsetFontdict(topDict);
    } else {
      this.createCIDFontdict(topDict);
    }

    var top = {
      version: 1,
      hdrSize: this.cff.hdrSize,
      offSize: 4,
      header: this.cff.header,
      nameIndex: [this.cff.postscriptName],
      topDictIndex: [topDict],
      stringIndex: this.strings,
      globalSubrIndex: this.gsubrs
    };

    CFFTop.encode(stream, top);
  };

  return CFFSubset;
}(Subset);

var _class;
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) {
  var desc = {};
  Object['ke' + 'ys'](descriptor).forEach(function (key) {
    desc[key] = descriptor[key];
  });
  desc.enumerable = !!desc.enumerable;
  desc.configurable = !!desc.configurable;

  if ('value' in desc || desc.initializer) {
    desc.writable = true;
  }

  desc = decorators.slice().reverse().reduce(function (desc, decorator) {
    return decorator(target, property, desc) || desc;
  }, desc);

  if (context && desc.initializer !== void 0) {
    desc.value = desc.initializer ? desc.initializer.call(context) : void 0;
    desc.initializer = undefined;
  }

  if (desc.initializer === void 0) {
    Object['define' + 'Property'](target, property, desc);
    desc = null;
  }

  return desc;
}

/**
 * This is the base class for all SFNT-based font formats in fontkit.
 * It supports TrueType, and PostScript glyphs, and several color glyph formats.
 */
var TTFFont = (_class = function () {
  TTFFont.probe = function probe(buffer) {
    var format = buffer.toString('ascii', 0, 4);
    return format === 'true' || format === 'OTTO' || format === String.fromCharCode(0, 1, 0, 0);
  };

  function TTFFont(stream) {
    var variationCoords = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

    _classCallCheck(this, TTFFont);

    this.defaultLanguage = null;
    this.stream = stream;
    this.variationCoords = variationCoords;

    this._directoryPos = this.stream.pos;
    this._tables = {};
    this._glyphs = {};
    this._decodeDirectory();

    // define properties for each table to lazily parse
    for (var tag in this.directory.tables) {
      var table = this.directory.tables[tag];
      if (tables[tag] && table.length > 0) {
        _Object$defineProperty(this, tag, {
          get: this._getTable.bind(this, table)
        });
      }
    }
  }

  TTFFont.prototype.setDefaultLanguage = function setDefaultLanguage() {
    var lang = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

    this.defaultLanguage = lang;
  };

  TTFFont.prototype._getTable = function _getTable(table) {
    if (!(table.tag in this._tables)) {
      try {
        this._tables[table.tag] = this._decodeTable(table);
      } catch (e) {
        if (fontkit.logErrors) {
          console.error('Error decoding table ' + table.tag);
          console.error(e.stack);
        }
      }
    }

    return this._tables[table.tag];
  };

  TTFFont.prototype._getTableStream = function _getTableStream(tag) {
    var table = this.directory.tables[tag];
    if (table) {
      this.stream.pos = table.offset;
      return this.stream;
    }

    return null;
  };

  TTFFont.prototype._decodeDirectory = function _decodeDirectory() {
    return this.directory = Directory.decode(this.stream, { _startOffset: 0 });
  };

  TTFFont.prototype._decodeTable = function _decodeTable(table) {
    var pos = this.stream.pos;

    var stream = this._getTableStream(table.tag);
    var result = tables[table.tag].decode(stream, this, table.length);

    this.stream.pos = pos;
    return result;
  };

  /**
   * Gets a string from the font's `name` table
   * `lang` is a BCP-47 language code.
   * @return {string}
   */


  TTFFont.prototype.getName = function getName(key) {
    var lang = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.defaultLanguage || fontkit.defaultLanguage;

    var record = this.name && this.name.records[key];
    if (record) {
      // Attempt to retrieve the entry, depending on which translation is available:
      return record[lang] || record[this.defaultLanguage] || record[fontkit.defaultLanguage] || record['en'] || record[_Object$keys(record)[0]] // Seriously, ANY language would be fine
      || null;
    }

    return null;
  };

  /**
   * The unique PostScript name for this font, e.g. "Helvetica-Bold"
   * @type {string}
   */


  /**
   * Returns whether there is glyph in the font for the given unicode code point.
   *
   * @param {number} codePoint
   * @return {boolean}
   */
  TTFFont.prototype.hasGlyphForCodePoint = function hasGlyphForCodePoint(codePoint) {
    return !!this._cmapProcessor.lookup(codePoint);
  };

  /**
   * Maps a single unicode code point to a Glyph object.
   * Does not perform any advanced substitutions (there is no context to do so).
   *
   * @param {number} codePoint
   * @return {Glyph}
   */


  TTFFont.prototype.glyphForCodePoint = function glyphForCodePoint(codePoint) {
    return this.getGlyph(this._cmapProcessor.lookup(codePoint), [codePoint]);
  };

  /**
   * Returns an array of Glyph objects for the given string.
   * This is only a one-to-one mapping from characters to glyphs.
   * For most uses, you should use font.layout (described below), which
   * provides a much more advanced mapping supporting AAT and OpenType shaping.
   *
   * @param {string} string
   * @return {Glyph[]}
   */


  TTFFont.prototype.glyphsForString = function glyphsForString(string) {
    var glyphs = [];
    var len = string.length;
    var idx = 0;
    var last = -1;
    var state = -1;

    while (idx <= len) {
      var code = 0;
      var nextState = 0;

      if (idx < len) {
        // Decode the next codepoint from UTF 16
        code = string.charCodeAt(idx++);
        if (0xd800 <= code && code <= 0xdbff && idx < len) {
          var next = string.charCodeAt(idx);
          if (0xdc00 <= next && next <= 0xdfff) {
            idx++;
            code = ((code & 0x3ff) << 10) + (next & 0x3ff) + 0x10000;
          }
        }

        // Compute the next state: 1 if the next codepoint is a variation selector, 0 otherwise.
        nextState = 0xfe00 <= code && code <= 0xfe0f || 0xe0100 <= code && code <= 0xe01ef ? 1 : 0;
      } else {
        idx++;
      }

      if (state === 0 && nextState === 1) {
        // Variation selector following normal codepoint.
        glyphs.push(this.getGlyph(this._cmapProcessor.lookup(last, code), [last, code]));
      } else if (state === 0 && nextState === 0) {
        // Normal codepoint following normal codepoint.
        glyphs.push(this.glyphForCodePoint(last));
      }

      last = code;
      state = nextState;
    }

    return glyphs;
  };

  /**
   * Returns a GlyphRun object, which includes an array of Glyphs and GlyphPositions for the given string.
   *
   * @param {string} string
   * @param {string[]} [userFeatures]
   * @param {string} [script]
   * @param {string} [language]
   * @param {string} [direction]
   * @return {GlyphRun}
   */
  TTFFont.prototype.layout = function layout(string, userFeatures, script, language, direction) {
    return this._layoutEngine.layout(string, userFeatures, script, language, direction);
  };

  /**
   * Returns an array of strings that map to the given glyph id.
   * @param {number} gid - glyph id
   */


  TTFFont.prototype.stringsForGlyph = function stringsForGlyph(gid) {
    return this._layoutEngine.stringsForGlyph(gid);
  };

  /**
   * An array of all [OpenType feature tags](https://www.microsoft.com/typography/otspec/featuretags.htm)
   * (or mapped AAT tags) supported by the font.
   * The features parameter is an array of OpenType feature tags to be applied in addition to the default set.
   * If this is an AAT font, the OpenType feature tags are mapped to AAT features.
   *
   * @type {string[]}
   */


  TTFFont.prototype.getAvailableFeatures = function getAvailableFeatures(script, language) {
    return this._layoutEngine.getAvailableFeatures(script, language);
  };

  TTFFont.prototype._getBaseGlyph = function _getBaseGlyph(glyph) {
    var characters = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

    if (!this._glyphs[glyph]) {
      if (this.directory.tables.glyf) {
        this._glyphs[glyph] = new TTFGlyph(glyph, characters, this);
      } else if (this.directory.tables['CFF '] || this.directory.tables.CFF2) {
        this._glyphs[glyph] = new CFFGlyph(glyph, characters, this);
      }
    }

    return this._glyphs[glyph] || null;
  };

  /**
   * Returns a glyph object for the given glyph id.
   * You can pass the array of code points this glyph represents for
   * your use later, and it will be stored in the glyph object.
   *
   * @param {number} glyph
   * @param {number[]} characters
   * @return {Glyph}
   */


  TTFFont.prototype.getGlyph = function getGlyph(glyph) {
    var characters = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

    if (!this._glyphs[glyph]) {
      if (this.directory.tables.sbix) {
        this._glyphs[glyph] = new SBIXGlyph(glyph, characters, this);
      } else if (this.directory.tables.COLR && this.directory.tables.CPAL) {
        this._glyphs[glyph] = new COLRGlyph(glyph, characters, this);
      } else {
        this._getBaseGlyph(glyph, characters);
      }
    }

    return this._glyphs[glyph] || null;
  };

  /**
   * Returns a Subset for this font.
   * @return {Subset}
   */


  TTFFont.prototype.createSubset = function createSubset() {
    if (this.directory.tables['CFF ']) {
      return new CFFSubset(this);
    }

    return new TTFSubset(this);
  };

  /**
   * Returns an object describing the available variation axes
   * that this font supports. Keys are setting tags, and values
   * contain the axis name, range, and default value.
   *
   * @type {object}
   */


  /**
   * Returns a new font with the given variation settings applied.
   * Settings can either be an instance name, or an object containing
   * variation tags as specified by the `variationAxes` property.
   *
   * @param {object} settings
   * @return {TTFFont}
   */
  TTFFont.prototype.getVariation = function getVariation(settings) {
    if (!(this.directory.tables.fvar && (this.directory.tables.gvar && this.directory.tables.glyf || this.directory.tables.CFF2))) {
      throw new Error('Variations require a font with the fvar, gvar and glyf, or CFF2 tables.');
    }

    if (typeof settings === 'string') {
      settings = this.namedVariations[settings];
    }

    if ((typeof settings === 'undefined' ? 'undefined' : _typeof(settings)) !== 'object') {
      throw new Error('Variation settings must be either a variation name or settings object.');
    }

    // normalize the coordinates
    var coords = this.fvar.axis.map(function (axis, i) {
      var axisTag = axis.axisTag.trim();
      if (axisTag in settings) {
        return Math.max(axis.minValue, Math.min(axis.maxValue, settings[axisTag]));
      } else {
        return axis.defaultValue;
      }
    });

    var stream = new r.DecodeStream(this.stream.buffer);
    stream.pos = this._directoryPos;

    var font = new TTFFont(stream, coords);
    font._tables = this._tables;

    return font;
  };

  // Standardized format plugin API
  TTFFont.prototype.getFont = function getFont(name) {
    return this.getVariation(name);
  };

  _createClass(TTFFont, [{
    key: 'postscriptName',
    get: function get() {
      return this.getName('postscriptName');
    }

    /**
     * The font's full name, e.g. "Helvetica Bold"
     * @type {string}
     */

  }, {
    key: 'fullName',
    get: function get() {
      return this.getName('fullName');
    }

    /**
     * The font's family name, e.g. "Helvetica"
     * @type {string}
     */

  }, {
    key: 'familyName',
    get: function get() {
      return this.getName('fontFamily');
    }

    /**
     * The font's sub-family, e.g. "Bold".
     * @type {string}
     */

  }, {
    key: 'subfamilyName',
    get: function get() {
      return this.getName('fontSubfamily');
    }

    /**
     * The font's copyright information
     * @type {string}
     */

  }, {
    key: 'copyright',
    get: function get() {
      return this.getName('copyright');
    }

    /**
     * The font's version number
     * @type {string}
     */

  }, {
    key: 'version',
    get: function get() {
      return this.getName('version');
    }

    /**
     * The font’s [ascender](https://en.wikipedia.org/wiki/Ascender_(typography))
     * @type {number}
     */

  }, {
    key: 'ascent',
    get: function get() {
      return this.hhea.ascent;
    }

    /**
     * The font’s [descender](https://en.wikipedia.org/wiki/Descender)
     * @type {number}
     */

  }, {
    key: 'descent',
    get: function get() {
      return this.hhea.descent;
    }

    /**
     * The amount of space that should be included between lines
     * @type {number}
     */

  }, {
    key: 'lineGap',
    get: function get() {
      return this.hhea.lineGap;
    }

    /**
     * The offset from the normal underline position that should be used
     * @type {number}
     */

  }, {
    key: 'underlinePosition',
    get: function get() {
      return this.post.underlinePosition;
    }

    /**
     * The weight of the underline that should be used
     * @type {number}
     */

  }, {
    key: 'underlineThickness',
    get: function get() {
      return this.post.underlineThickness;
    }

    /**
     * If this is an italic font, the angle the cursor should be drawn at to match the font design
     * @type {number}
     */

  }, {
    key: 'italicAngle',
    get: function get() {
      return this.post.italicAngle;
    }

    /**
     * The height of capital letters above the baseline.
     * See [here](https://en.wikipedia.org/wiki/Cap_height) for more details.
     * @type {number}
     */

  }, {
    key: 'capHeight',
    get: function get() {
      var os2 = this['OS/2'];
      return os2 ? os2.capHeight : this.ascent;
    }

    /**
     * The height of lower case letters in the font.
     * See [here](https://en.wikipedia.org/wiki/X-height) for more details.
     * @type {number}
     */

  }, {
    key: 'xHeight',
    get: function get() {
      var os2 = this['OS/2'];
      return os2 ? os2.xHeight : 0;
    }

    /**
     * The number of glyphs in the font.
     * @type {number}
     */

  }, {
    key: 'numGlyphs',
    get: function get() {
      return this.maxp.numGlyphs;
    }

    /**
     * The size of the font’s internal coordinate grid
     * @type {number}
     */

  }, {
    key: 'unitsPerEm',
    get: function get() {
      return this.head.unitsPerEm;
    }

    /**
     * The font’s bounding box, i.e. the box that encloses all glyphs in the font.
     * @type {BBox}
     */

  }, {
    key: 'bbox',
    get: function get() {
      return _Object$freeze(new BBox(this.head.xMin, this.head.yMin, this.head.xMax, this.head.yMax));
    }
  }, {
    key: '_cmapProcessor',
    get: function get() {
      return new CmapProcessor(this.cmap);
    }

    /**
     * An array of all of the unicode code points supported by the font.
     * @type {number[]}
     */

  }, {
    key: 'characterSet',
    get: function get() {
      return this._cmapProcessor.getCharacterSet();
    }
  }, {
    key: '_layoutEngine',
    get: function get() {
      return new LayoutEngine(this);
    }
  }, {
    key: 'availableFeatures',
    get: function get() {
      return this._layoutEngine.getAvailableFeatures();
    }
  }, {
    key: 'variationAxes',
    get: function get() {
      var res = {};
      if (!this.fvar) {
        return res;
      }

      for (var _iterator = this.fvar.axis, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
        var _ref;

        if (_isArray) {
          if (_i >= _iterator.length) break;
          _ref = _iterator[_i++];
        } else {
          _i = _iterator.next();
          if (_i.done) break;
          _ref = _i.value;
        }

        var axis = _ref;

        res[axis.axisTag.trim()] = {
          name: axis.name.en,
          min: axis.minValue,
          default: axis.defaultValue,
          max: axis.maxValue
        };
      }

      return res;
    }

    /**
     * Returns an object describing the named variation instances
     * that the font designer has specified. Keys are variation names
     * and values are the variation settings for this instance.
     *
     * @type {object}
     */

  }, {
    key: 'namedVariations',
    get: function get() {
      var res = {};
      if (!this.fvar) {
        return res;
      }

      for (var _iterator2 = this.fvar.instance, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
        var _ref2;

        if (_isArray2) {
          if (_i2 >= _iterator2.length) break;
          _ref2 = _iterator2[_i2++];
        } else {
          _i2 = _iterator2.next();
          if (_i2.done) break;
          _ref2 = _i2.value;
        }

        var instance = _ref2;

        var settings = {};
        for (var i = 0; i < this.fvar.axis.length; i++) {
          var axis = this.fvar.axis[i];
          settings[axis.axisTag.trim()] = instance.coord[i];
        }

        res[instance.name.en] = settings;
      }

      return res;
    }
  }, {
    key: '_variationProcessor',
    get: function get() {
      if (!this.fvar) {
        return null;
      }

      var variationCoords = this.variationCoords;

      // Ignore if no variation coords and not CFF2
      if (!variationCoords && !this.CFF2) {
        return null;
      }

      if (!variationCoords) {
        variationCoords = this.fvar.axis.map(function (axis) {
          return axis.defaultValue;
        });
      }

      return new GlyphVariationProcessor(this, variationCoords);
    }
  }]);

  return TTFFont;
}(), (_applyDecoratedDescriptor(_class.prototype, 'bbox', [cache], _Object$getOwnPropertyDescriptor(_class.prototype, 'bbox'), _class.prototype), _applyDecoratedDescriptor(_class.prototype, '_cmapProcessor', [cache], _Object$getOwnPropertyDescriptor(_class.prototype, '_cmapProcessor'), _class.prototype), _applyDecoratedDescriptor(_class.prototype, 'characterSet', [cache], _Object$getOwnPropertyDescriptor(_class.prototype, 'characterSet'), _class.prototype), _applyDecoratedDescriptor(_class.prototype, '_layoutEngine', [cache], _Object$getOwnPropertyDescriptor(_class.prototype, '_layoutEngine'), _class.prototype), _applyDecoratedDescriptor(_class.prototype, 'variationAxes', [cache], _Object$getOwnPropertyDescriptor(_class.prototype, 'variationAxes'), _class.prototype), _applyDecoratedDescriptor(_class.prototype, 'namedVariations', [cache], _Object$getOwnPropertyDescriptor(_class.prototype, 'namedVariations'), _class.prototype), _applyDecoratedDescriptor(_class.prototype, '_variationProcessor', [cache], _Object$getOwnPropertyDescriptor(_class.prototype, '_variationProcessor'), _class.prototype)), _class);

var WOFFDirectoryEntry = new r.Struct({
  tag: new r.String(4),
  offset: new r.Pointer(r.uint32, 'void', { type: 'global' }),
  compLength: r.uint32,
  length: r.uint32,
  origChecksum: r.uint32
});

var WOFFDirectory = new r.Struct({
  tag: new r.String(4), // should be 'wOFF'
  flavor: r.uint32,
  length: r.uint32,
  numTables: r.uint16,
  reserved: new r.Reserved(r.uint16),
  totalSfntSize: r.uint32,
  majorVersion: r.uint16,
  minorVersion: r.uint16,
  metaOffset: r.uint32,
  metaLength: r.uint32,
  metaOrigLength: r.uint32,
  privOffset: r.uint32,
  privLength: r.uint32,
  tables: new r.Array(WOFFDirectoryEntry, 'numTables')
});

WOFFDirectory.process = function () {
  var tables = {};
  for (var _iterator = this.tables, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var table = _ref;

    tables[table.tag] = table;
  }

  this.tables = tables;
};

var WOFFFont = function (_TTFFont) {
  _inherits(WOFFFont, _TTFFont);

  function WOFFFont() {
    _classCallCheck(this, WOFFFont);

    return _possibleConstructorReturn(this, _TTFFont.apply(this, arguments));
  }

  WOFFFont.probe = function probe(buffer) {
    return buffer.toString('ascii', 0, 4) === 'wOFF';
  };

  WOFFFont.prototype._decodeDirectory = function _decodeDirectory() {
    this.directory = WOFFDirectory.decode(this.stream, { _startOffset: 0 });
  };

  WOFFFont.prototype._getTableStream = function _getTableStream(tag) {
    var table = this.directory.tables[tag];
    if (table) {
      this.stream.pos = table.offset;

      if (table.compLength < table.length) {
        this.stream.pos += 2; // skip deflate header
        var outBuffer = new Buffer(table.length);
        var buf = inflate(this.stream.readBuffer(table.compLength - 2), outBuffer);
        return new r.DecodeStream(buf);
      } else {
        return this.stream;
      }
    }

    return null;
  };

  return WOFFFont;
}(TTFFont);

/**
 * Represents a TrueType glyph in the WOFF2 format, which compresses glyphs differently.
 */

var WOFF2Glyph = function (_TTFGlyph) {
  _inherits(WOFF2Glyph, _TTFGlyph);

  function WOFF2Glyph() {
    _classCallCheck(this, WOFF2Glyph);

    return _possibleConstructorReturn(this, _TTFGlyph.apply(this, arguments));
  }

  WOFF2Glyph.prototype._decode = function _decode() {
    // We have to decode in advance (in WOFF2Font), so just return the pre-decoded data.
    return this._font._transformedGlyphs[this.id];
  };

  WOFF2Glyph.prototype._getCBox = function _getCBox() {
    return this.path.bbox;
  };

  return WOFF2Glyph;
}(TTFGlyph);

var Base128 = {
  decode: function decode(stream) {
    var result = 0;
    var iterable = [0, 1, 2, 3, 4];
    for (var j = 0; j < iterable.length; j++) {
      var i = iterable[j];
      var code = stream.readUInt8();

      // If any of the top seven bits are set then we're about to overflow.
      if (result & 0xe0000000) {
        throw new Error('Overflow');
      }

      result = result << 7 | code & 0x7f;
      if ((code & 0x80) === 0) {
        return result;
      }
    }

    throw new Error('Bad base 128 number');
  }
};

var knownTags = ['cmap', 'head', 'hhea', 'hmtx', 'maxp', 'name', 'OS/2', 'post', 'cvt ', 'fpgm', 'glyf', 'loca', 'prep', 'CFF ', 'VORG', 'EBDT', 'EBLC', 'gasp', 'hdmx', 'kern', 'LTSH', 'PCLT', 'VDMX', 'vhea', 'vmtx', 'BASE', 'GDEF', 'GPOS', 'GSUB', 'EBSC', 'JSTF', 'MATH', 'CBDT', 'CBLC', 'COLR', 'CPAL', 'SVG ', 'sbix', 'acnt', 'avar', 'bdat', 'bloc', 'bsln', 'cvar', 'fdsc', 'feat', 'fmtx', 'fvar', 'gvar', 'hsty', 'just', 'lcar', 'mort', 'morx', 'opbd', 'prop', 'trak', 'Zapf', 'Silf', 'Glat', 'Gloc', 'Feat', 'Sill'];

var WOFF2DirectoryEntry = new r.Struct({
  flags: r.uint8,
  customTag: new r.Optional(new r.String(4), function (t) {
    return (t.flags & 0x3f) === 0x3f;
  }),
  tag: function tag(t) {
    return t.customTag || knownTags[t.flags & 0x3f];
  }, // || (() => { throw new Error(`Bad tag: ${flags & 0x3f}`); })(); },
  length: Base128,
  transformVersion: function transformVersion(t) {
    return t.flags >>> 6 & 0x03;
  },
  transformed: function transformed(t) {
    return t.tag === 'glyf' || t.tag === 'loca' ? t.transformVersion === 0 : t.transformVersion !== 0;
  },
  transformLength: new r.Optional(Base128, function (t) {
    return t.transformed;
  })
});

var WOFF2Directory = new r.Struct({
  tag: new r.String(4), // should be 'wOF2'
  flavor: r.uint32,
  length: r.uint32,
  numTables: r.uint16,
  reserved: new r.Reserved(r.uint16),
  totalSfntSize: r.uint32,
  totalCompressedSize: r.uint32,
  majorVersion: r.uint16,
  minorVersion: r.uint16,
  metaOffset: r.uint32,
  metaLength: r.uint32,
  metaOrigLength: r.uint32,
  privOffset: r.uint32,
  privLength: r.uint32,
  tables: new r.Array(WOFF2DirectoryEntry, 'numTables')
});

WOFF2Directory.process = function () {
  var tables = {};
  for (var i = 0; i < this.tables.length; i++) {
    var table = this.tables[i];
    tables[table.tag] = table;
  }

  return this.tables = tables;
};

/**
 * Subclass of TTFFont that represents a TTF/OTF font compressed by WOFF2
 * See spec here: http://www.w3.org/TR/WOFF2/
 */

var WOFF2Font = function (_TTFFont) {
  _inherits(WOFF2Font, _TTFFont);

  function WOFF2Font() {
    _classCallCheck(this, WOFF2Font);

    return _possibleConstructorReturn(this, _TTFFont.apply(this, arguments));
  }

  WOFF2Font.probe = function probe(buffer) {
    return buffer.toString('ascii', 0, 4) === 'wOF2';
  };

  WOFF2Font.prototype._decodeDirectory = function _decodeDirectory() {
    this.directory = WOFF2Directory.decode(this.stream);
    this._dataPos = this.stream.pos;
  };

  WOFF2Font.prototype._decompress = function _decompress() {
    // decompress data and setup table offsets if we haven't already
    if (!this._decompressed) {
      this.stream.pos = this._dataPos;
      var buffer = this.stream.readBuffer(this.directory.totalCompressedSize);

      var decompressedSize = 0;
      for (var tag in this.directory.tables) {
        var entry = this.directory.tables[tag];
        entry.offset = decompressedSize;
        decompressedSize += entry.transformLength != null ? entry.transformLength : entry.length;
      }

      var decompressed = brotli(buffer, decompressedSize);
      if (!decompressed) {
        throw new Error('Error decoding compressed data in WOFF2');
      }

      this.stream = new r.DecodeStream(new Buffer(decompressed));
      this._decompressed = true;
    }
  };

  WOFF2Font.prototype._decodeTable = function _decodeTable(table) {
    this._decompress();
    return _TTFFont.prototype._decodeTable.call(this, table);
  };

  // Override this method to get a glyph and return our
  // custom subclass if there is a glyf table.


  WOFF2Font.prototype._getBaseGlyph = function _getBaseGlyph(glyph) {
    var characters = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

    if (!this._glyphs[glyph]) {
      if (this.directory.tables.glyf && this.directory.tables.glyf.transformed) {
        if (!this._transformedGlyphs) {
          this._transformGlyfTable();
        }
        return this._glyphs[glyph] = new WOFF2Glyph(glyph, characters, this);
      } else {
        return _TTFFont.prototype._getBaseGlyph.call(this, glyph, characters);
      }
    }
  };

  WOFF2Font.prototype._transformGlyfTable = function _transformGlyfTable() {
    this._decompress();
    this.stream.pos = this.directory.tables.glyf.offset;
    var table = GlyfTable.decode(this.stream);
    var glyphs = [];

    for (var index = 0; index < table.numGlyphs; index++) {
      var glyph = {};
      var nContours = table.nContours.readInt16BE();
      glyph.numberOfContours = nContours;

      if (nContours > 0) {
        // simple glyph
        var nPoints = [];
        var totalPoints = 0;

        for (var i = 0; i < nContours; i++) {
          var _r = read255UInt16(table.nPoints);
          totalPoints += _r;
          nPoints.push(totalPoints);
        }

        glyph.points = decodeTriplet(table.flags, table.glyphs, totalPoints);
        for (var _i = 0; _i < nContours; _i++) {
          glyph.points[nPoints[_i] - 1].endContour = true;
        }

        var instructionSize = read255UInt16(table.glyphs);
      } else if (nContours < 0) {
        // composite glyph
        var haveInstructions = TTFGlyph.prototype._decodeComposite.call({ _font: this }, glyph, table.composites);
        if (haveInstructions) {
          var instructionSize = read255UInt16(table.glyphs);
        }
      }

      glyphs.push(glyph);
    }

    this._transformedGlyphs = glyphs;
  };

  return WOFF2Font;
}(TTFFont);

var Substream = function () {
  function Substream(length) {
    _classCallCheck(this, Substream);

    this.length = length;
    this._buf = new r.Buffer(length);
  }

  Substream.prototype.decode = function decode(stream, parent) {
    return new r.DecodeStream(this._buf.decode(stream, parent));
  };

  return Substream;
}();

// This struct represents the entire glyf table


var GlyfTable = new r.Struct({
  version: r.uint32,
  numGlyphs: r.uint16,
  indexFormat: r.uint16,
  nContourStreamSize: r.uint32,
  nPointsStreamSize: r.uint32,
  flagStreamSize: r.uint32,
  glyphStreamSize: r.uint32,
  compositeStreamSize: r.uint32,
  bboxStreamSize: r.uint32,
  instructionStreamSize: r.uint32,
  nContours: new Substream('nContourStreamSize'),
  nPoints: new Substream('nPointsStreamSize'),
  flags: new Substream('flagStreamSize'),
  glyphs: new Substream('glyphStreamSize'),
  composites: new Substream('compositeStreamSize'),
  bboxes: new Substream('bboxStreamSize'),
  instructions: new Substream('instructionStreamSize')
});

var WORD_CODE = 253;
var ONE_MORE_BYTE_CODE2 = 254;
var ONE_MORE_BYTE_CODE1 = 255;
var LOWEST_U_CODE = 253;

function read255UInt16(stream) {
  var code = stream.readUInt8();

  if (code === WORD_CODE) {
    return stream.readUInt16BE();
  }

  if (code === ONE_MORE_BYTE_CODE1) {
    return stream.readUInt8() + LOWEST_U_CODE;
  }

  if (code === ONE_MORE_BYTE_CODE2) {
    return stream.readUInt8() + LOWEST_U_CODE * 2;
  }

  return code;
}

function withSign(flag, baseval) {
  return flag & 1 ? baseval : -baseval;
}

function decodeTriplet(flags, glyphs, nPoints) {
  var y = void 0;
  var x = y = 0;
  var res = [];

  for (var i = 0; i < nPoints; i++) {
    var dx = 0,
        dy = 0;
    var flag = flags.readUInt8();
    var onCurve = !(flag >> 7);
    flag &= 0x7f;

    if (flag < 10) {
      dx = 0;
      dy = withSign(flag, ((flag & 14) << 7) + glyphs.readUInt8());
    } else if (flag < 20) {
      dx = withSign(flag, ((flag - 10 & 14) << 7) + glyphs.readUInt8());
      dy = 0;
    } else if (flag < 84) {
      var b0 = flag - 20;
      var b1 = glyphs.readUInt8();
      dx = withSign(flag, 1 + (b0 & 0x30) + (b1 >> 4));
      dy = withSign(flag >> 1, 1 + ((b0 & 0x0c) << 2) + (b1 & 0x0f));
    } else if (flag < 120) {
      var b0 = flag - 84;
      dx = withSign(flag, 1 + (b0 / 12 << 8) + glyphs.readUInt8());
      dy = withSign(flag >> 1, 1 + (b0 % 12 >> 2 << 8) + glyphs.readUInt8());
    } else if (flag < 124) {
      var b1 = glyphs.readUInt8();
      var b2 = glyphs.readUInt8();
      dx = withSign(flag, (b1 << 4) + (b2 >> 4));
      dy = withSign(flag >> 1, ((b2 & 0x0f) << 8) + glyphs.readUInt8());
    } else {
      dx = withSign(flag, glyphs.readUInt16BE());
      dy = withSign(flag >> 1, glyphs.readUInt16BE());
    }

    x += dx;
    y += dy;
    res.push(new Point(onCurve, false, x, y));
  }

  return res;
}

var TTCHeader = new r.VersionedStruct(r.uint32, {
  0x00010000: {
    numFonts: r.uint32,
    offsets: new r.Array(r.uint32, 'numFonts')
  },
  0x00020000: {
    numFonts: r.uint32,
    offsets: new r.Array(r.uint32, 'numFonts'),
    dsigTag: r.uint32,
    dsigLength: r.uint32,
    dsigOffset: r.uint32
  }
});

var TrueTypeCollection = function () {
  TrueTypeCollection.probe = function probe(buffer) {
    return buffer.toString('ascii', 0, 4) === 'ttcf';
  };

  function TrueTypeCollection(stream) {
    _classCallCheck(this, TrueTypeCollection);

    this.stream = stream;
    if (stream.readString(4) !== 'ttcf') {
      throw new Error('Not a TrueType collection');
    }

    this.header = TTCHeader.decode(stream);
  }

  TrueTypeCollection.prototype.getFont = function getFont(name) {
    for (var _iterator = this.header.offsets, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var offset = _ref;

      var stream = new r.DecodeStream(this.stream.buffer);
      stream.pos = offset;
      var font = new TTFFont(stream);
      if (font.postscriptName === name) {
        return font;
      }
    }

    return null;
  };

  _createClass(TrueTypeCollection, [{
    key: 'fonts',
    get: function get() {
      var fonts = [];
      for (var _iterator2 = this.header.offsets, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
        var _ref2;

        if (_isArray2) {
          if (_i2 >= _iterator2.length) break;
          _ref2 = _iterator2[_i2++];
        } else {
          _i2 = _iterator2.next();
          if (_i2.done) break;
          _ref2 = _i2.value;
        }

        var offset = _ref2;

        var stream = new r.DecodeStream(this.stream.buffer);
        stream.pos = offset;
        fonts.push(new TTFFont(stream));
      }

      return fonts;
    }
  }]);

  return TrueTypeCollection;
}();

var DFontName = new r.String(r.uint8);
var DFontData = new r.Struct({
  len: r.uint32,
  buf: new r.Buffer('len')
});

var Ref = new r.Struct({
  id: r.uint16,
  nameOffset: r.int16,
  attr: r.uint8,
  dataOffset: r.uint24,
  handle: r.uint32
});

var Type = new r.Struct({
  name: new r.String(4),
  maxTypeIndex: r.uint16,
  refList: new r.Pointer(r.uint16, new r.Array(Ref, function (t) {
    return t.maxTypeIndex + 1;
  }), { type: 'parent' })
});

var TypeList = new r.Struct({
  length: r.uint16,
  types: new r.Array(Type, function (t) {
    return t.length + 1;
  })
});

var DFontMap = new r.Struct({
  reserved: new r.Reserved(r.uint8, 24),
  typeList: new r.Pointer(r.uint16, TypeList),
  nameListOffset: new r.Pointer(r.uint16, 'void')
});

var DFontHeader = new r.Struct({
  dataOffset: r.uint32,
  map: new r.Pointer(r.uint32, DFontMap),
  dataLength: r.uint32,
  mapLength: r.uint32
});

var DFont = function () {
  DFont.probe = function probe(buffer) {
    var stream = new r.DecodeStream(buffer);

    try {
      var header = DFontHeader.decode(stream);
    } catch (e) {
      return false;
    }

    for (var _iterator = header.map.typeList.types, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _getIterator(_iterator);;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var type = _ref;

      if (type.name === 'sfnt') {
        return true;
      }
    }

    return false;
  };

  function DFont(stream) {
    _classCallCheck(this, DFont);

    this.stream = stream;
    this.header = DFontHeader.decode(this.stream);

    for (var _iterator2 = this.header.map.typeList.types, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _getIterator(_iterator2);;) {
      var _ref2;

      if (_isArray2) {
        if (_i2 >= _iterator2.length) break;
        _ref2 = _iterator2[_i2++];
      } else {
        _i2 = _iterator2.next();
        if (_i2.done) break;
        _ref2 = _i2.value;
      }

      var type = _ref2;

      for (var _iterator3 = type.refList, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _getIterator(_iterator3);;) {
        var _ref3;

        if (_isArray3) {
          if (_i3 >= _iterator3.length) break;
          _ref3 = _iterator3[_i3++];
        } else {
          _i3 = _iterator3.next();
          if (_i3.done) break;
          _ref3 = _i3.value;
        }

        var ref = _ref3;

        if (ref.nameOffset >= 0) {
          this.stream.pos = ref.nameOffset + this.header.map.nameListOffset;
          ref.name = DFontName.decode(this.stream);
        } else {
          ref.name = null;
        }
      }

      if (type.name === 'sfnt') {
        this.sfnt = type;
      }
    }
  }

  DFont.prototype.getFont = function getFont(name) {
    if (!this.sfnt) {
      return null;
    }

    for (var _iterator4 = this.sfnt.refList, _isArray4 = Array.isArray(_iterator4), _i4 = 0, _iterator4 = _isArray4 ? _iterator4 : _getIterator(_iterator4);;) {
      var _ref4;

      if (_isArray4) {
        if (_i4 >= _iterator4.length) break;
        _ref4 = _iterator4[_i4++];
      } else {
        _i4 = _iterator4.next();
        if (_i4.done) break;
        _ref4 = _i4.value;
      }

      var ref = _ref4;

      var pos = this.header.dataOffset + ref.dataOffset + 4;
      var stream = new r.DecodeStream(this.stream.buffer.slice(pos));
      var font = new TTFFont(stream);
      if (font.postscriptName === name) {
        return font;
      }
    }

    return null;
  };

  _createClass(DFont, [{
    key: 'fonts',
    get: function get() {
      var fonts = [];
      for (var _iterator5 = this.sfnt.refList, _isArray5 = Array.isArray(_iterator5), _i5 = 0, _iterator5 = _isArray5 ? _iterator5 : _getIterator(_iterator5);;) {
        var _ref5;

        if (_isArray5) {
          if (_i5 >= _iterator5.length) break;
          _ref5 = _iterator5[_i5++];
        } else {
          _i5 = _iterator5.next();
          if (_i5.done) break;
          _ref5 = _i5.value;
        }

        var ref = _ref5;

        var pos = this.header.dataOffset + ref.dataOffset + 4;
        var stream = new r.DecodeStream(this.stream.buffer.slice(pos));
        fonts.push(new TTFFont(stream));
      }

      return fonts;
    }
  }]);

  return DFont;
}();

// Register font formats
fontkit.registerFormat(TTFFont);
fontkit.registerFormat(WOFFFont);
fontkit.registerFormat(WOFF2Font);
fontkit.registerFormat(TrueTypeCollection);
fontkit.registerFormat(DFont);

module.exports = fontkit;
//# sourceMappingURL=index.js.map

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer))

/***/ }),
/* 387 */
/***/ (function(module, exports, __webpack_require__) {

// Generated by CoffeeScript 1.7.1
(function() {
  var key, val, _ref, _ref1;

  exports.EncodeStream = __webpack_require__(388);

  exports.DecodeStream = __webpack_require__(169);

  exports.Array = __webpack_require__(252);

  exports.LazyArray = __webpack_require__(407);

  exports.Bitfield = __webpack_require__(408);

  exports.Boolean = __webpack_require__(409);

  exports.Buffer = __webpack_require__(410);

  exports.Enum = __webpack_require__(411);

  exports.Optional = __webpack_require__(412);

  exports.Reserved = __webpack_require__(413);

  exports.String = __webpack_require__(414);

  exports.Struct = __webpack_require__(253);

  exports.VersionedStruct = __webpack_require__(415);

  _ref = __webpack_require__(89);
  for (key in _ref) {
    val = _ref[key];
    exports[key] = val;
  }

  _ref1 = __webpack_require__(416);
  for (key in _ref1) {
    val = _ref1[key];
    exports[key] = val;
  }

}).call(this);


/***/ }),
/* 388 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(Buffer) {// Generated by CoffeeScript 1.7.1
(function() {
  var DecodeStream, EncodeStream, iconv, stream,
    __hasProp = {}.hasOwnProperty,
    __extends = function(child, parent) { for (var key in parent) { if (__hasProp.call(parent, key)) child[key] = parent[key]; } function ctor() { this.constructor = child; } ctor.prototype = parent.prototype; child.prototype = new ctor(); child.__super__ = parent.prototype; return child; };

  stream = __webpack_require__(121);

  DecodeStream = __webpack_require__(169);

  try {
    iconv = __webpack_require__(170);
  } catch (_error) {}

  EncodeStream = (function(_super) {
    var key;

    __extends(EncodeStream, _super);

    function EncodeStream(bufferSize) {
      if (bufferSize == null) {
        bufferSize = 65536;
      }
      EncodeStream.__super__.constructor.apply(this, arguments);
      this.buffer = new Buffer(bufferSize);
      this.bufferOffset = 0;
      this.pos = 0;
    }

    for (key in Buffer.prototype) {
      if (key.slice(0, 5) === 'write') {
        (function(key) {
          var bytes;
          bytes = +DecodeStream.TYPES[key.replace(/write|[BL]E/g, '')];
          return EncodeStream.prototype[key] = function(value) {
            this.ensure(bytes);
            this.buffer[key](value, this.bufferOffset);
            this.bufferOffset += bytes;
            return this.pos += bytes;
          };
        })(key);
      }
    }

    EncodeStream.prototype._read = function() {};

    EncodeStream.prototype.ensure = function(bytes) {
      if (this.bufferOffset + bytes > this.buffer.length) {
        return this.flush();
      }
    };

    EncodeStream.prototype.flush = function() {
      if (this.bufferOffset > 0) {
        this.push(new Buffer(this.buffer.slice(0, this.bufferOffset)));
        return this.bufferOffset = 0;
      }
    };

    EncodeStream.prototype.writeBuffer = function(buffer) {
      this.flush();
      this.push(buffer);
      return this.pos += buffer.length;
    };

    EncodeStream.prototype.writeString = function(string, encoding) {
      var buf, byte, i, _i, _ref;
      if (encoding == null) {
        encoding = 'ascii';
      }
      switch (encoding) {
        case 'utf16le':
        case 'ucs2':
        case 'utf8':
        case 'ascii':
          return this.writeBuffer(new Buffer(string, encoding));
        case 'utf16be':
          buf = new Buffer(string, 'utf16le');
          for (i = _i = 0, _ref = buf.length - 1; _i < _ref; i = _i += 2) {
            byte = buf[i];
            buf[i] = buf[i + 1];
            buf[i + 1] = byte;
          }
          return this.writeBuffer(buf);
        default:
          if (iconv) {
            return this.writeBuffer(iconv.encode(string, encoding));
          } else {
            throw new Error('Install iconv-lite to enable additional string encodings.');
          }
      }
    };

    EncodeStream.prototype.writeUInt24BE = function(val) {
      this.ensure(3);
      this.buffer[this.bufferOffset++] = val >>> 16 & 0xff;
      this.buffer[this.bufferOffset++] = val >>> 8 & 0xff;
      this.buffer[this.bufferOffset++] = val & 0xff;
      return this.pos += 3;
    };

    EncodeStream.prototype.writeUInt24LE = function(val) {
      this.ensure(3);
      this.buffer[this.bufferOffset++] = val & 0xff;
      this.buffer[this.bufferOffset++] = val >>> 8 & 0xff;
      this.buffer[this.bufferOffset++] = val >>> 16 & 0xff;
      return this.pos += 3;
    };

    EncodeStream.prototype.writeInt24BE = function(val) {
      if (val >= 0) {
        return this.writeUInt24BE(val);
      } else {
        return this.writeUInt24BE(val + 0xffffff + 1);
      }
    };

    EncodeStream.prototype.writeInt24LE = function(val) {
      if (val >= 0) {
        return this.writeUInt24LE(val);
      } else {
        return this.writeUInt24LE(val + 0xffffff + 1);
      }
    };

    EncodeStream.prototype.fill = function(val, length) {
      var buf;
      if (length < this.buffer.length) {
        this.ensure(length);
        this.buffer.fill(val, this.bufferOffset, this.bufferOffset + length);
        this.bufferOffset += length;
        return this.pos += length;
      } else {
        buf = new Buffer(length);
        buf.fill(val);
        return this.writeBuffer(buf);
      }
    };

    EncodeStream.prototype.end = function() {
      this.flush();
      return this.push(null);
    };

    return EncodeStream;

  })(stream.Readable);

  module.exports = EncodeStream;

}).call(this);

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer))

/***/ }),
/* 389 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var BOMChar = '\uFEFF';

exports.PrependBOM = PrependBOMWrapper
function PrependBOMWrapper(encoder, options) {
    this.encoder = encoder;
    this.addBOM = true;
}

PrependBOMWrapper.prototype.write = function(str) {
    if (this.addBOM) {
        str = BOMChar + str;
        this.addBOM = false;
    }

    return this.encoder.write(str);
}

PrependBOMWrapper.prototype.end = function() {
    return this.encoder.end();
}


//------------------------------------------------------------------------------

exports.StripBOM = StripBOMWrapper;
function StripBOMWrapper(decoder, options) {
    this.decoder = decoder;
    this.pass = false;
    this.options = options || {};
}

StripBOMWrapper.prototype.write = function(buf) {
    var res = this.decoder.write(buf);
    if (this.pass || !res)
        return res;

    if (res[0] === BOMChar) {
        res = res.slice(1);
        if (typeof this.options.stripBOM === 'function')
            this.options.stripBOM();
    }

    this.pass = true;
    return res;
}

StripBOMWrapper.prototype.end = function() {
    return this.decoder.end();
}



/***/ }),
/* 390 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// Update this array if you add/rename/remove files in this directory.
// We support Browserify by skipping automatic module discovery and requiring modules directly.
var modules = [
    __webpack_require__(391),
    __webpack_require__(392),
    __webpack_require__(393),
    __webpack_require__(394),
    __webpack_require__(395),
    __webpack_require__(396),
    __webpack_require__(397),
    __webpack_require__(398),
    __webpack_require__(399),
];

// Put all encoding/alias/codec definitions to single object and export it.
for (var i = 0; i < modules.length; i++) {
    var module = modules[i];
    for (var enc in module)
        if (Object.prototype.hasOwnProperty.call(module, enc))
            exports[enc] = module[enc];
}


/***/ }),
/* 391 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var Buffer = __webpack_require__(39).Buffer;

// Export Node.js internal encodings.

module.exports = {
    // Encodings
    utf8:   { type: "_internal", bomAware: true},
    cesu8:  { type: "_internal", bomAware: true},
    unicode11utf8: "utf8",

    ucs2:   { type: "_internal", bomAware: true},
    utf16le: "ucs2",

    binary: { type: "_internal" },
    base64: { type: "_internal" },
    hex:    { type: "_internal" },

    // Codec.
    _internal: InternalCodec,
};

//------------------------------------------------------------------------------

function InternalCodec(codecOptions, iconv) {
    this.enc = codecOptions.encodingName;
    this.bomAware = codecOptions.bomAware;

    if (this.enc === "base64")
        this.encoder = InternalEncoderBase64;
    else if (this.enc === "cesu8") {
        this.enc = "utf8"; // Use utf8 for decoding.
        this.encoder = InternalEncoderCesu8;

        // Add decoder for versions of Node not supporting CESU-8
        if (Buffer.from('eda0bdedb2a9', 'hex').toString() !== '💩') {
            this.decoder = InternalDecoderCesu8;
            this.defaultCharUnicode = iconv.defaultCharUnicode;
        }
    }
}

InternalCodec.prototype.encoder = InternalEncoder;
InternalCodec.prototype.decoder = InternalDecoder;

//------------------------------------------------------------------------------

// We use node.js internal decoder. Its signature is the same as ours.
var StringDecoder = __webpack_require__(125).StringDecoder;

if (!StringDecoder.prototype.end) // Node v0.8 doesn't have this method.
    StringDecoder.prototype.end = function() {};


function InternalDecoder(options, codec) {
    this.decoder = new StringDecoder(codec.enc);
}

InternalDecoder.prototype.write = function(buf) {
    if (!Buffer.isBuffer(buf)) {
        buf = Buffer.from(buf);
    }

    return this.decoder.write(buf);
}

InternalDecoder.prototype.end = function() {
    return this.decoder.end();
}


//------------------------------------------------------------------------------
// Encoder is mostly trivial

function InternalEncoder(options, codec) {
    this.enc = codec.enc;
}

InternalEncoder.prototype.write = function(str) {
    return Buffer.from(str, this.enc);
}

InternalEncoder.prototype.end = function() {
}


//------------------------------------------------------------------------------
// Except base64 encoder, which must keep its state.

function InternalEncoderBase64(options, codec) {
    this.prevStr = '';
}

InternalEncoderBase64.prototype.write = function(str) {
    str = this.prevStr + str;
    var completeQuads = str.length - (str.length % 4);
    this.prevStr = str.slice(completeQuads);
    str = str.slice(0, completeQuads);

    return Buffer.from(str, "base64");
}

InternalEncoderBase64.prototype.end = function() {
    return Buffer.from(this.prevStr, "base64");
}


//------------------------------------------------------------------------------
// CESU-8 encoder is also special.

function InternalEncoderCesu8(options, codec) {
}

InternalEncoderCesu8.prototype.write = function(str) {
    var buf = Buffer.alloc(str.length * 3), bufIdx = 0;
    for (var i = 0; i < str.length; i++) {
        var charCode = str.charCodeAt(i);
        // Naive implementation, but it works because CESU-8 is especially easy
        // to convert from UTF-16 (which all JS strings are encoded in).
        if (charCode < 0x80)
            buf[bufIdx++] = charCode;
        else if (charCode < 0x800) {
            buf[bufIdx++] = 0xC0 + (charCode >>> 6);
            buf[bufIdx++] = 0x80 + (charCode & 0x3f);
        }
        else { // charCode will always be < 0x10000 in javascript.
            buf[bufIdx++] = 0xE0 + (charCode >>> 12);
            buf[bufIdx++] = 0x80 + ((charCode >>> 6) & 0x3f);
            buf[bufIdx++] = 0x80 + (charCode & 0x3f);
        }
    }
    return buf.slice(0, bufIdx);
}

InternalEncoderCesu8.prototype.end = function() {
}

//------------------------------------------------------------------------------
// CESU-8 decoder is not implemented in Node v4.0+

function InternalDecoderCesu8(options, codec) {
    this.acc = 0;
    this.contBytes = 0;
    this.accBytes = 0;
    this.defaultCharUnicode = codec.defaultCharUnicode;
}

InternalDecoderCesu8.prototype.write = function(buf) {
    var acc = this.acc, contBytes = this.contBytes, accBytes = this.accBytes, 
        res = '';
    for (var i = 0; i < buf.length; i++) {
        var curByte = buf[i];
        if ((curByte & 0xC0) !== 0x80) { // Leading byte
            if (contBytes > 0) { // Previous code is invalid
                res += this.defaultCharUnicode;
                contBytes = 0;
            }

            if (curByte < 0x80) { // Single-byte code
                res += String.fromCharCode(curByte);
            } else if (curByte < 0xE0) { // Two-byte code
                acc = curByte & 0x1F;
                contBytes = 1; accBytes = 1;
            } else if (curByte < 0xF0) { // Three-byte code
                acc = curByte & 0x0F;
                contBytes = 2; accBytes = 1;
            } else { // Four or more are not supported for CESU-8.
                res += this.defaultCharUnicode;
            }
        } else { // Continuation byte
            if (contBytes > 0) { // We're waiting for it.
                acc = (acc << 6) | (curByte & 0x3f);
                contBytes--; accBytes++;
                if (contBytes === 0) {
                    // Check for overlong encoding, but support Modified UTF-8 (encoding NULL as C0 80)
                    if (accBytes === 2 && acc < 0x80 && acc > 0)
                        res += this.defaultCharUnicode;
                    else if (accBytes === 3 && acc < 0x800)
                        res += this.defaultCharUnicode;
                    else
                        // Actually add character.
                        res += String.fromCharCode(acc);
                }
            } else { // Unexpected continuation byte
                res += this.defaultCharUnicode;
            }
        }
    }
    this.acc = acc; this.contBytes = contBytes; this.accBytes = accBytes;
    return res;
}

InternalDecoderCesu8.prototype.end = function() {
    var res = 0;
    if (this.contBytes > 0)
        res += this.defaultCharUnicode;
    return res;
}


/***/ }),
/* 392 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var Buffer = __webpack_require__(39).Buffer;

// == UTF32-LE/BE codec. ==========================================================

exports._utf32 = Utf32Codec;

function Utf32Codec(codecOptions, iconv) {
    this.iconv = iconv;
    this.bomAware = true;
    this.isLE = codecOptions.isLE;
}

exports.utf32le = { type: '_utf32', isLE: true };
exports.utf32be = { type: '_utf32', isLE: false };

// Aliases
exports.ucs4le = 'utf32le';
exports.ucs4be = 'utf32be';

Utf32Codec.prototype.encoder = Utf32Encoder;
Utf32Codec.prototype.decoder = Utf32Decoder;

// -- Encoding

function Utf32Encoder(options, codec) {
    this.isLE = codec.isLE;
    this.highSurrogate = 0;
}

Utf32Encoder.prototype.write = function(str) {
    var src = Buffer.from(str, 'ucs2');
    var dst = Buffer.alloc(src.length * 2);
    var write32 = this.isLE ? dst.writeUInt32LE : dst.writeUInt32BE;
    var offset = 0;

    for (var i = 0; i < src.length; i += 2) {
        var code = src.readUInt16LE(i);
        var isHighSurrogate = (0xD800 <= code && code < 0xDC00);
        var isLowSurrogate = (0xDC00 <= code && code < 0xE000);

        if (this.highSurrogate) {
            if (isHighSurrogate || !isLowSurrogate) {
                // There shouldn't be two high surrogates in a row, nor a high surrogate which isn't followed by a low
                // surrogate. If this happens, keep the pending high surrogate as a stand-alone semi-invalid character
                // (technically wrong, but expected by some applications, like Windows file names).
                write32.call(dst, this.highSurrogate, offset);
                offset += 4;
            }
            else {
                // Create 32-bit value from high and low surrogates;
                var codepoint = (((this.highSurrogate - 0xD800) << 10) | (code - 0xDC00)) + 0x10000;

                write32.call(dst, codepoint, offset);
                offset += 4;
                this.highSurrogate = 0;

                continue;
            }
        }

        if (isHighSurrogate)
            this.highSurrogate = code;
        else {
            // Even if the current character is a low surrogate, with no previous high surrogate, we'll
            // encode it as a semi-invalid stand-alone character for the same reasons expressed above for
            // unpaired high surrogates.
            write32.call(dst, code, offset);
            offset += 4;
            this.highSurrogate = 0;
        }
    }

    if (offset < dst.length)
        dst = dst.slice(0, offset);

    return dst;
};

Utf32Encoder.prototype.end = function() {
    // Treat any leftover high surrogate as a semi-valid independent character.
    if (!this.highSurrogate)
        return;

    var buf = Buffer.alloc(4);

    if (this.isLE)
        buf.writeUInt32LE(this.highSurrogate, 0);
    else
        buf.writeUInt32BE(this.highSurrogate, 0);

    this.highSurrogate = 0;

    return buf;
};

// -- Decoding

function Utf32Decoder(options, codec) {
    this.isLE = codec.isLE;
    this.badChar = codec.iconv.defaultCharUnicode.charCodeAt(0);
    this.overflow = [];
}

Utf32Decoder.prototype.write = function(src) {
    if (src.length === 0)
        return '';

    var i = 0;
    var codepoint = 0;
    var dst = Buffer.alloc(src.length + 4);
    var offset = 0;
    var isLE = this.isLE;
    var overflow = this.overflow;
    var badChar = this.badChar;

    if (overflow.length > 0) {
        for (; i < src.length && overflow.length < 4; i++)
            overflow.push(src[i]);
        
        if (overflow.length === 4) {
            // NOTE: codepoint is a signed int32 and can be negative.
            // NOTE: We copied this block from below to help V8 optimize it (it works with array, not buffer).
            if (isLE) {
                codepoint = overflow[i] | (overflow[i+1] << 8) | (overflow[i+2] << 16) | (overflow[i+3] << 24);
            } else {
                codepoint = overflow[i+3] | (overflow[i+2] << 8) | (overflow[i+1] << 16) | (overflow[i] << 24);
            }
            overflow.length = 0;

            offset = _writeCodepoint(dst, offset, codepoint, badChar);
        }
    }

    // Main loop. Should be as optimized as possible.
    for (; i < src.length - 3; i += 4) {
        // NOTE: codepoint is a signed int32 and can be negative.
        if (isLE) {
            codepoint = src[i] | (src[i+1] << 8) | (src[i+2] << 16) | (src[i+3] << 24);
        } else {
            codepoint = src[i+3] | (src[i+2] << 8) | (src[i+1] << 16) | (src[i] << 24);
        }
        offset = _writeCodepoint(dst, offset, codepoint, badChar);
    }

    // Keep overflowing bytes.
    for (; i < src.length; i++) {
        overflow.push(src[i]);
    }

    return dst.slice(0, offset).toString('ucs2');
};

function _writeCodepoint(dst, offset, codepoint, badChar) {
    // NOTE: codepoint is signed int32 and can be negative. We keep it that way to help V8 with optimizations.
    if (codepoint < 0 || codepoint > 0x10FFFF) {
        // Not a valid Unicode codepoint
        codepoint = badChar;
    } 

    // Ephemeral Planes: Write high surrogate.
    if (codepoint >= 0x10000) {
        codepoint -= 0x10000;

        var high = 0xD800 | (codepoint >> 10);
        dst[offset++] = high & 0xff;
        dst[offset++] = high >> 8;

        // Low surrogate is written below.
        var codepoint = 0xDC00 | (codepoint & 0x3FF);
    }

    // Write BMP char or low surrogate.
    dst[offset++] = codepoint & 0xff;
    dst[offset++] = codepoint >> 8;

    return offset;
};

Utf32Decoder.prototype.end = function() {
    this.overflow.length = 0;
};

// == UTF-32 Auto codec =============================================================
// Decoder chooses automatically from UTF-32LE and UTF-32BE using BOM and space-based heuristic.
// Defaults to UTF-32LE. http://en.wikipedia.org/wiki/UTF-32
// Encoder/decoder default can be changed: iconv.decode(buf, 'utf32', {defaultEncoding: 'utf-32be'});

// Encoder prepends BOM (which can be overridden with (addBOM: false}).

exports.utf32 = Utf32AutoCodec;
exports.ucs4 = 'utf32';

function Utf32AutoCodec(options, iconv) {
    this.iconv = iconv;
}

Utf32AutoCodec.prototype.encoder = Utf32AutoEncoder;
Utf32AutoCodec.prototype.decoder = Utf32AutoDecoder;

// -- Encoding

function Utf32AutoEncoder(options, codec) {
    options = options || {};

    if (options.addBOM === undefined)
        options.addBOM = true;

    this.encoder = codec.iconv.getEncoder(options.defaultEncoding || 'utf-32le', options);
}

Utf32AutoEncoder.prototype.write = function(str) {
    return this.encoder.write(str);
};

Utf32AutoEncoder.prototype.end = function() {
    return this.encoder.end();
};

// -- Decoding

function Utf32AutoDecoder(options, codec) {
    this.decoder = null;
    this.initialBufs = [];
    this.initialBufsLen = 0;
    this.options = options || {};
    this.iconv = codec.iconv;
}

Utf32AutoDecoder.prototype.write = function(buf) {
    if (!this.decoder) { 
        // Codec is not chosen yet. Accumulate initial bytes.
        this.initialBufs.push(buf);
        this.initialBufsLen += buf.length;

        if (this.initialBufsLen < 32) // We need more bytes to use space heuristic (see below)
            return '';

        // We have enough bytes -> detect endianness.
        var encoding = detectEncoding(this.initialBufs, this.options.defaultEncoding);
        this.decoder = this.iconv.getDecoder(encoding, this.options);

        var resStr = '';
        for (var i = 0; i < this.initialBufs.length; i++)
            resStr += this.decoder.write(this.initialBufs[i]);

        this.initialBufs.length = this.initialBufsLen = 0;
        return resStr;
    }

    return this.decoder.write(buf);
};

Utf32AutoDecoder.prototype.end = function() {
    if (!this.decoder) {
        var encoding = detectEncoding(this.initialBufs, this.options.defaultEncoding);
        this.decoder = this.iconv.getDecoder(encoding, this.options);

        var resStr = '';
        for (var i = 0; i < this.initialBufs.length; i++)
            resStr += this.decoder.write(this.initialBufs[i]);

        var trail = this.decoder.end();
        if (trail)
            resStr += trail;

        this.initialBufs.length = this.initialBufsLen = 0;
        return resStr;
    }

    return this.decoder.end();
};

function detectEncoding(bufs, defaultEncoding) {
    var b = [];
    var charsProcessed = 0;
    var invalidLE = 0, invalidBE = 0;   // Number of invalid chars when decoded as LE or BE.
    var bmpCharsLE = 0, bmpCharsBE = 0; // Number of BMP chars when decoded as LE or BE.

    outer_loop:
    for (var i = 0; i < bufs.length; i++) {
        var buf = bufs[i];
        for (var j = 0; j < buf.length; j++) {
            b.push(buf[j]);
            if (b.length === 4) {
                if (charsProcessed === 0) {
                    // Check BOM first.
                    if (b[0] === 0xFF && b[1] === 0xFE && b[2] === 0 && b[3] === 0) {
                        return 'utf-32le';
                    }
                    if (b[0] === 0 && b[1] === 0 && b[2] === 0xFE && b[3] === 0xFF) {
                        return 'utf-32be';
                    }
                }

                if (b[0] !== 0 || b[1] > 0x10) invalidBE++;
                if (b[3] !== 0 || b[2] > 0x10) invalidLE++;

                if (b[0] === 0 && b[1] === 0 && (b[2] !== 0 || b[3] !== 0)) bmpCharsBE++;
                if ((b[0] !== 0 || b[1] !== 0) && b[2] === 0 && b[3] === 0) bmpCharsLE++;

                b.length = 0;
                charsProcessed++;

                if (charsProcessed >= 100) {
                    break outer_loop;
                }
            }
        }
    }

    // Make decisions.
    if (bmpCharsBE - invalidBE > bmpCharsLE - invalidLE)  return 'utf-32be';
    if (bmpCharsBE - invalidBE < bmpCharsLE - invalidLE)  return 'utf-32le';

    // Couldn't decide (likely all zeros or not enough data).
    return defaultEncoding || 'utf-32le';
}


/***/ }),
/* 393 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var Buffer = __webpack_require__(39).Buffer;

// Note: UTF16-LE (or UCS2) codec is Node.js native. See encodings/internal.js

// == UTF16-BE codec. ==========================================================

exports.utf16be = Utf16BECodec;
function Utf16BECodec() {
}

Utf16BECodec.prototype.encoder = Utf16BEEncoder;
Utf16BECodec.prototype.decoder = Utf16BEDecoder;
Utf16BECodec.prototype.bomAware = true;


// -- Encoding

function Utf16BEEncoder() {
}

Utf16BEEncoder.prototype.write = function(str) {
    var buf = Buffer.from(str, 'ucs2');
    for (var i = 0; i < buf.length; i += 2) {
        var tmp = buf[i]; buf[i] = buf[i+1]; buf[i+1] = tmp;
    }
    return buf;
}

Utf16BEEncoder.prototype.end = function() {
}


// -- Decoding

function Utf16BEDecoder() {
    this.overflowByte = -1;
}

Utf16BEDecoder.prototype.write = function(buf) {
    if (buf.length == 0)
        return '';

    var buf2 = Buffer.alloc(buf.length + 1),
        i = 0, j = 0;

    if (this.overflowByte !== -1) {
        buf2[0] = buf[0];
        buf2[1] = this.overflowByte;
        i = 1; j = 2;
    }

    for (; i < buf.length-1; i += 2, j+= 2) {
        buf2[j] = buf[i+1];
        buf2[j+1] = buf[i];
    }

    this.overflowByte = (i == buf.length-1) ? buf[buf.length-1] : -1;

    return buf2.slice(0, j).toString('ucs2');
}

Utf16BEDecoder.prototype.end = function() {
    this.overflowByte = -1;
}


// == UTF-16 codec =============================================================
// Decoder chooses automatically from UTF-16LE and UTF-16BE using BOM and space-based heuristic.
// Defaults to UTF-16LE, as it's prevalent and default in Node.
// http://en.wikipedia.org/wiki/UTF-16 and http://encoding.spec.whatwg.org/#utf-16le
// Decoder default can be changed: iconv.decode(buf, 'utf16', {defaultEncoding: 'utf-16be'});

// Encoder uses UTF-16LE and prepends BOM (which can be overridden with addBOM: false).

exports.utf16 = Utf16Codec;
function Utf16Codec(codecOptions, iconv) {
    this.iconv = iconv;
}

Utf16Codec.prototype.encoder = Utf16Encoder;
Utf16Codec.prototype.decoder = Utf16Decoder;


// -- Encoding (pass-through)

function Utf16Encoder(options, codec) {
    options = options || {};
    if (options.addBOM === undefined)
        options.addBOM = true;
    this.encoder = codec.iconv.getEncoder('utf-16le', options);
}

Utf16Encoder.prototype.write = function(str) {
    return this.encoder.write(str);
}

Utf16Encoder.prototype.end = function() {
    return this.encoder.end();
}


// -- Decoding

function Utf16Decoder(options, codec) {
    this.decoder = null;
    this.initialBufs = [];
    this.initialBufsLen = 0;

    this.options = options || {};
    this.iconv = codec.iconv;
}

Utf16Decoder.prototype.write = function(buf) {
    if (!this.decoder) {
        // Codec is not chosen yet. Accumulate initial bytes.
        this.initialBufs.push(buf);
        this.initialBufsLen += buf.length;
        
        if (this.initialBufsLen < 16) // We need more bytes to use space heuristic (see below)
            return '';

        // We have enough bytes -> detect endianness.
        var encoding = detectEncoding(this.initialBufs, this.options.defaultEncoding);
        this.decoder = this.iconv.getDecoder(encoding, this.options);

        var resStr = '';
        for (var i = 0; i < this.initialBufs.length; i++)
            resStr += this.decoder.write(this.initialBufs[i]);

        this.initialBufs.length = this.initialBufsLen = 0;
        return resStr;
    }

    return this.decoder.write(buf);
}

Utf16Decoder.prototype.end = function() {
    if (!this.decoder) {
        var encoding = detectEncoding(this.initialBufs, this.options.defaultEncoding);
        this.decoder = this.iconv.getDecoder(encoding, this.options);

        var resStr = '';
        for (var i = 0; i < this.initialBufs.length; i++)
            resStr += this.decoder.write(this.initialBufs[i]);

        var trail = this.decoder.end();
        if (trail)
            resStr += trail;

        this.initialBufs.length = this.initialBufsLen = 0;
        return resStr;
    }
    return this.decoder.end();
}

function detectEncoding(bufs, defaultEncoding) {
    var b = [];
    var charsProcessed = 0;
    var asciiCharsLE = 0, asciiCharsBE = 0; // Number of ASCII chars when decoded as LE or BE.

    outer_loop:
    for (var i = 0; i < bufs.length; i++) {
        var buf = bufs[i];
        for (var j = 0; j < buf.length; j++) {
            b.push(buf[j]);
            if (b.length === 2) {
                if (charsProcessed === 0) {
                    // Check BOM first.
                    if (b[0] === 0xFF && b[1] === 0xFE) return 'utf-16le';
                    if (b[0] === 0xFE && b[1] === 0xFF) return 'utf-16be';
                }

                if (b[0] === 0 && b[1] !== 0) asciiCharsBE++;
                if (b[0] !== 0 && b[1] === 0) asciiCharsLE++;

                b.length = 0;
                charsProcessed++;

                if (charsProcessed >= 100) {
                    break outer_loop;
                }
            }
        }
    }

    // Make decisions.
    // Most of the time, the content has ASCII chars (U+00**), but the opposite (U+**00) is uncommon.
    // So, we count ASCII as if it was LE or BE, and decide from that.
    if (asciiCharsBE > asciiCharsLE) return 'utf-16be';
    if (asciiCharsBE < asciiCharsLE) return 'utf-16le';

    // Couldn't decide (likely all zeros or not enough data).
    return defaultEncoding || 'utf-16le';
}




/***/ }),
/* 394 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var Buffer = __webpack_require__(39).Buffer;

// UTF-7 codec, according to https://tools.ietf.org/html/rfc2152
// See also below a UTF-7-IMAP codec, according to http://tools.ietf.org/html/rfc3501#section-5.1.3

exports.utf7 = Utf7Codec;
exports.unicode11utf7 = 'utf7'; // Alias UNICODE-1-1-UTF-7
function Utf7Codec(codecOptions, iconv) {
    this.iconv = iconv;
};

Utf7Codec.prototype.encoder = Utf7Encoder;
Utf7Codec.prototype.decoder = Utf7Decoder;
Utf7Codec.prototype.bomAware = true;


// -- Encoding

var nonDirectChars = /[^A-Za-z0-9'\(\),-\.\/:\? \n\r\t]+/g;

function Utf7Encoder(options, codec) {
    this.iconv = codec.iconv;
}

Utf7Encoder.prototype.write = function(str) {
    // Naive implementation.
    // Non-direct chars are encoded as "+<base64>-"; single "+" char is encoded as "+-".
    return Buffer.from(str.replace(nonDirectChars, function(chunk) {
        return "+" + (chunk === '+' ? '' : 
            this.iconv.encode(chunk, 'utf16-be').toString('base64').replace(/=+$/, '')) 
            + "-";
    }.bind(this)));
}

Utf7Encoder.prototype.end = function() {
}


// -- Decoding

function Utf7Decoder(options, codec) {
    this.iconv = codec.iconv;
    this.inBase64 = false;
    this.base64Accum = '';
}

var base64Regex = /[A-Za-z0-9\/+]/;
var base64Chars = [];
for (var i = 0; i < 256; i++)
    base64Chars[i] = base64Regex.test(String.fromCharCode(i));

var plusChar = '+'.charCodeAt(0), 
    minusChar = '-'.charCodeAt(0),
    andChar = '&'.charCodeAt(0);

Utf7Decoder.prototype.write = function(buf) {
    var res = "", lastI = 0,
        inBase64 = this.inBase64,
        base64Accum = this.base64Accum;

    // The decoder is more involved as we must handle chunks in stream.

    for (var i = 0; i < buf.length; i++) {
        if (!inBase64) { // We're in direct mode.
            // Write direct chars until '+'
            if (buf[i] == plusChar) {
                res += this.iconv.decode(buf.slice(lastI, i), "ascii"); // Write direct chars.
                lastI = i+1;
                inBase64 = true;
            }
        } else { // We decode base64.
            if (!base64Chars[buf[i]]) { // Base64 ended.
                if (i == lastI && buf[i] == minusChar) {// "+-" -> "+"
                    res += "+";
                } else {
                    var b64str = base64Accum + this.iconv.decode(buf.slice(lastI, i), "ascii");
                    res += this.iconv.decode(Buffer.from(b64str, 'base64'), "utf16-be");
                }

                if (buf[i] != minusChar) // Minus is absorbed after base64.
                    i--;

                lastI = i+1;
                inBase64 = false;
                base64Accum = '';
            }
        }
    }

    if (!inBase64) {
        res += this.iconv.decode(buf.slice(lastI), "ascii"); // Write direct chars.
    } else {
        var b64str = base64Accum + this.iconv.decode(buf.slice(lastI), "ascii");

        var canBeDecoded = b64str.length - (b64str.length % 8); // Minimal chunk: 2 quads -> 2x3 bytes -> 3 chars.
        base64Accum = b64str.slice(canBeDecoded); // The rest will be decoded in future.
        b64str = b64str.slice(0, canBeDecoded);

        res += this.iconv.decode(Buffer.from(b64str, 'base64'), "utf16-be");
    }

    this.inBase64 = inBase64;
    this.base64Accum = base64Accum;

    return res;
}

Utf7Decoder.prototype.end = function() {
    var res = "";
    if (this.inBase64 && this.base64Accum.length > 0)
        res = this.iconv.decode(Buffer.from(this.base64Accum, 'base64'), "utf16-be");

    this.inBase64 = false;
    this.base64Accum = '';
    return res;
}


// UTF-7-IMAP codec.
// RFC3501 Sec. 5.1.3 Modified UTF-7 (http://tools.ietf.org/html/rfc3501#section-5.1.3)
// Differences:
//  * Base64 part is started by "&" instead of "+"
//  * Direct characters are 0x20-0x7E, except "&" (0x26)
//  * In Base64, "," is used instead of "/"
//  * Base64 must not be used to represent direct characters.
//  * No implicit shift back from Base64 (should always end with '-')
//  * String must end in non-shifted position.
//  * "-&" while in base64 is not allowed.


exports.utf7imap = Utf7IMAPCodec;
function Utf7IMAPCodec(codecOptions, iconv) {
    this.iconv = iconv;
};

Utf7IMAPCodec.prototype.encoder = Utf7IMAPEncoder;
Utf7IMAPCodec.prototype.decoder = Utf7IMAPDecoder;
Utf7IMAPCodec.prototype.bomAware = true;


// -- Encoding

function Utf7IMAPEncoder(options, codec) {
    this.iconv = codec.iconv;
    this.inBase64 = false;
    this.base64Accum = Buffer.alloc(6);
    this.base64AccumIdx = 0;
}

Utf7IMAPEncoder.prototype.write = function(str) {
    var inBase64 = this.inBase64,
        base64Accum = this.base64Accum,
        base64AccumIdx = this.base64AccumIdx,
        buf = Buffer.alloc(str.length*5 + 10), bufIdx = 0;

    for (var i = 0; i < str.length; i++) {
        var uChar = str.charCodeAt(i);
        if (0x20 <= uChar && uChar <= 0x7E) { // Direct character or '&'.
            if (inBase64) {
                if (base64AccumIdx > 0) {
                    bufIdx += buf.write(base64Accum.slice(0, base64AccumIdx).toString('base64').replace(/\//g, ',').replace(/=+$/, ''), bufIdx);
                    base64AccumIdx = 0;
                }

                buf[bufIdx++] = minusChar; // Write '-', then go to direct mode.
                inBase64 = false;
            }

            if (!inBase64) {
                buf[bufIdx++] = uChar; // Write direct character

                if (uChar === andChar)  // Ampersand -> '&-'
                    buf[bufIdx++] = minusChar;
            }

        } else { // Non-direct character
            if (!inBase64) {
                buf[bufIdx++] = andChar; // Write '&', then go to base64 mode.
                inBase64 = true;
            }
            if (inBase64) {
                base64Accum[base64AccumIdx++] = uChar >> 8;
                base64Accum[base64AccumIdx++] = uChar & 0xFF;

                if (base64AccumIdx == base64Accum.length) {
                    bufIdx += buf.write(base64Accum.toString('base64').replace(/\//g, ','), bufIdx);
                    base64AccumIdx = 0;
                }
            }
        }
    }

    this.inBase64 = inBase64;
    this.base64AccumIdx = base64AccumIdx;

    return buf.slice(0, bufIdx);
}

Utf7IMAPEncoder.prototype.end = function() {
    var buf = Buffer.alloc(10), bufIdx = 0;
    if (this.inBase64) {
        if (this.base64AccumIdx > 0) {
            bufIdx += buf.write(this.base64Accum.slice(0, this.base64AccumIdx).toString('base64').replace(/\//g, ',').replace(/=+$/, ''), bufIdx);
            this.base64AccumIdx = 0;
        }

        buf[bufIdx++] = minusChar; // Write '-', then go to direct mode.
        this.inBase64 = false;
    }

    return buf.slice(0, bufIdx);
}


// -- Decoding

function Utf7IMAPDecoder(options, codec) {
    this.iconv = codec.iconv;
    this.inBase64 = false;
    this.base64Accum = '';
}

var base64IMAPChars = base64Chars.slice();
base64IMAPChars[','.charCodeAt(0)] = true;

Utf7IMAPDecoder.prototype.write = function(buf) {
    var res = "", lastI = 0,
        inBase64 = this.inBase64,
        base64Accum = this.base64Accum;

    // The decoder is more involved as we must handle chunks in stream.
    // It is forgiving, closer to standard UTF-7 (for example, '-' is optional at the end).

    for (var i = 0; i < buf.length; i++) {
        if (!inBase64) { // We're in direct mode.
            // Write direct chars until '&'
            if (buf[i] == andChar) {
                res += this.iconv.decode(buf.slice(lastI, i), "ascii"); // Write direct chars.
                lastI = i+1;
                inBase64 = true;
            }
        } else { // We decode base64.
            if (!base64IMAPChars[buf[i]]) { // Base64 ended.
                if (i == lastI && buf[i] == minusChar) { // "&-" -> "&"
                    res += "&";
                } else {
                    var b64str = base64Accum + this.iconv.decode(buf.slice(lastI, i), "ascii").replace(/,/g, '/');
                    res += this.iconv.decode(Buffer.from(b64str, 'base64'), "utf16-be");
                }

                if (buf[i] != minusChar) // Minus may be absorbed after base64.
                    i--;

                lastI = i+1;
                inBase64 = false;
                base64Accum = '';
            }
        }
    }

    if (!inBase64) {
        res += this.iconv.decode(buf.slice(lastI), "ascii"); // Write direct chars.
    } else {
        var b64str = base64Accum + this.iconv.decode(buf.slice(lastI), "ascii").replace(/,/g, '/');

        var canBeDecoded = b64str.length - (b64str.length % 8); // Minimal chunk: 2 quads -> 2x3 bytes -> 3 chars.
        base64Accum = b64str.slice(canBeDecoded); // The rest will be decoded in future.
        b64str = b64str.slice(0, canBeDecoded);

        res += this.iconv.decode(Buffer.from(b64str, 'base64'), "utf16-be");
    }

    this.inBase64 = inBase64;
    this.base64Accum = base64Accum;

    return res;
}

Utf7IMAPDecoder.prototype.end = function() {
    var res = "";
    if (this.inBase64 && this.base64Accum.length > 0)
        res = this.iconv.decode(Buffer.from(this.base64Accum, 'base64'), "utf16-be");

    this.inBase64 = false;
    this.base64Accum = '';
    return res;
}




/***/ }),
/* 395 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var Buffer = __webpack_require__(39).Buffer;

// Single-byte codec. Needs a 'chars' string parameter that contains 256 or 128 chars that
// correspond to encoded bytes (if 128 - then lower half is ASCII). 

exports._sbcs = SBCSCodec;
function SBCSCodec(codecOptions, iconv) {
    if (!codecOptions)
        throw new Error("SBCS codec is called without the data.")
    
    // Prepare char buffer for decoding.
    if (!codecOptions.chars || (codecOptions.chars.length !== 128 && codecOptions.chars.length !== 256))
        throw new Error("Encoding '"+codecOptions.type+"' has incorrect 'chars' (must be of len 128 or 256)");
    
    if (codecOptions.chars.length === 128) {
        var asciiString = "";
        for (var i = 0; i < 128; i++)
            asciiString += String.fromCharCode(i);
        codecOptions.chars = asciiString + codecOptions.chars;
    }

    this.decodeBuf = Buffer.from(codecOptions.chars, 'ucs2');
    
    // Encoding buffer.
    var encodeBuf = Buffer.alloc(65536, iconv.defaultCharSingleByte.charCodeAt(0));

    for (var i = 0; i < codecOptions.chars.length; i++)
        encodeBuf[codecOptions.chars.charCodeAt(i)] = i;

    this.encodeBuf = encodeBuf;
}

SBCSCodec.prototype.encoder = SBCSEncoder;
SBCSCodec.prototype.decoder = SBCSDecoder;


function SBCSEncoder(options, codec) {
    this.encodeBuf = codec.encodeBuf;
}

SBCSEncoder.prototype.write = function(str) {
    var buf = Buffer.alloc(str.length);
    for (var i = 0; i < str.length; i++)
        buf[i] = this.encodeBuf[str.charCodeAt(i)];
    
    return buf;
}

SBCSEncoder.prototype.end = function() {
}


function SBCSDecoder(options, codec) {
    this.decodeBuf = codec.decodeBuf;
}

SBCSDecoder.prototype.write = function(buf) {
    // Strings are immutable in JS -> we use ucs2 buffer to speed up computations.
    var decodeBuf = this.decodeBuf;
    var newBuf = Buffer.alloc(buf.length*2);
    var idx1 = 0, idx2 = 0;
    for (var i = 0; i < buf.length; i++) {
        idx1 = buf[i]*2; idx2 = i*2;
        newBuf[idx2] = decodeBuf[idx1];
        newBuf[idx2+1] = decodeBuf[idx1+1];
    }
    return newBuf.toString('ucs2');
}

SBCSDecoder.prototype.end = function() {
}


/***/ }),
/* 396 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// Manually added data to be used by sbcs codec in addition to generated one.

module.exports = {
    // Not supported by iconv, not sure why.
    "10029": "maccenteuro",
    "maccenteuro": {
        "type": "_sbcs",
        "chars": "ÄĀāÉĄÖÜáąČäčĆćéŹźĎíďĒēĖóėôöõúĚěü†°Ę£§•¶ß®©™ę¨≠ģĮįĪ≤≥īĶ∂∑łĻļĽľĹĺŅņŃ¬√ńŇ∆«»… ňŐÕőŌ–—“”‘’÷◊ōŔŕŘ‹›řŖŗŠ‚„šŚśÁŤťÍŽžŪÓÔūŮÚůŰűŲųÝýķŻŁżĢˇ"
    },

    "808": "cp808",
    "ibm808": "cp808",
    "cp808": {
        "type": "_sbcs",
        "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмноп░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀рстуфхцчшщъыьэюяЁёЄєЇїЎў°∙·√№€■ "
    },

    "mik": {
        "type": "_sbcs",
        "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя└┴┬├─┼╣║╚╔╩╦╠═╬┐░▒▓│┤№§╗╝┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
    },

    "cp720": {
        "type": "_sbcs",
        "chars": "\x80\x81éâ\x84à\x86çêëèïî\x8d\x8e\x8f\x90\u0651\u0652ô¤ـûùءآأؤ£إئابةتثجحخدذرزسشص«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀ضطظعغفµقكلمنهوىي≡\u064b\u064c\u064d\u064e\u064f\u0650≈°∙·√ⁿ²■\u00a0"
    },

    // Aliases of generated encodings.
    "ascii8bit": "ascii",
    "usascii": "ascii",
    "ansix34": "ascii",
    "ansix341968": "ascii",
    "ansix341986": "ascii",
    "csascii": "ascii",
    "cp367": "ascii",
    "ibm367": "ascii",
    "isoir6": "ascii",
    "iso646us": "ascii",
    "iso646irv": "ascii",
    "us": "ascii",

    "latin1": "iso88591",
    "latin2": "iso88592",
    "latin3": "iso88593",
    "latin4": "iso88594",
    "latin5": "iso88599",
    "latin6": "iso885910",
    "latin7": "iso885913",
    "latin8": "iso885914",
    "latin9": "iso885915",
    "latin10": "iso885916",

    "csisolatin1": "iso88591",
    "csisolatin2": "iso88592",
    "csisolatin3": "iso88593",
    "csisolatin4": "iso88594",
    "csisolatincyrillic": "iso88595",
    "csisolatinarabic": "iso88596",
    "csisolatingreek" : "iso88597",
    "csisolatinhebrew": "iso88598",
    "csisolatin5": "iso88599",
    "csisolatin6": "iso885910",

    "l1": "iso88591",
    "l2": "iso88592",
    "l3": "iso88593",
    "l4": "iso88594",
    "l5": "iso88599",
    "l6": "iso885910",
    "l7": "iso885913",
    "l8": "iso885914",
    "l9": "iso885915",
    "l10": "iso885916",

    "isoir14": "iso646jp",
    "isoir57": "iso646cn",
    "isoir100": "iso88591",
    "isoir101": "iso88592",
    "isoir109": "iso88593",
    "isoir110": "iso88594",
    "isoir144": "iso88595",
    "isoir127": "iso88596",
    "isoir126": "iso88597",
    "isoir138": "iso88598",
    "isoir148": "iso88599",
    "isoir157": "iso885910",
    "isoir166": "tis620",
    "isoir179": "iso885913",
    "isoir199": "iso885914",
    "isoir203": "iso885915",
    "isoir226": "iso885916",

    "cp819": "iso88591",
    "ibm819": "iso88591",

    "cyrillic": "iso88595",

    "arabic": "iso88596",
    "arabic8": "iso88596",
    "ecma114": "iso88596",
    "asmo708": "iso88596",

    "greek" : "iso88597",
    "greek8" : "iso88597",
    "ecma118" : "iso88597",
    "elot928" : "iso88597",

    "hebrew": "iso88598",
    "hebrew8": "iso88598",

    "turkish": "iso88599",
    "turkish8": "iso88599",

    "thai": "iso885911",
    "thai8": "iso885911",

    "celtic": "iso885914",
    "celtic8": "iso885914",
    "isoceltic": "iso885914",

    "tis6200": "tis620",
    "tis62025291": "tis620",
    "tis62025330": "tis620",

    "10000": "macroman",
    "10006": "macgreek",
    "10007": "maccyrillic",
    "10079": "maciceland",
    "10081": "macturkish",

    "cspc8codepage437": "cp437",
    "cspc775baltic": "cp775",
    "cspc850multilingual": "cp850",
    "cspcp852": "cp852",
    "cspc862latinhebrew": "cp862",
    "cpgr": "cp869",

    "msee": "cp1250",
    "mscyrl": "cp1251",
    "msansi": "cp1252",
    "msgreek": "cp1253",
    "msturk": "cp1254",
    "mshebr": "cp1255",
    "msarab": "cp1256",
    "winbaltrim": "cp1257",

    "cp20866": "koi8r",
    "20866": "koi8r",
    "ibm878": "koi8r",
    "cskoi8r": "koi8r",

    "cp21866": "koi8u",
    "21866": "koi8u",
    "ibm1168": "koi8u",

    "strk10482002": "rk1048",

    "tcvn5712": "tcvn",
    "tcvn57121": "tcvn",

    "gb198880": "iso646cn",
    "cn": "iso646cn",

    "csiso14jisc6220ro": "iso646jp",
    "jisc62201969ro": "iso646jp",
    "jp": "iso646jp",

    "cshproman8": "hproman8",
    "r8": "hproman8",
    "roman8": "hproman8",
    "xroman8": "hproman8",
    "ibm1051": "hproman8",

    "mac": "macintosh",
    "csmacintosh": "macintosh",
};



/***/ }),
/* 397 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// Generated data for sbcs codec. Don't edit manually. Regenerate using generation/gen-sbcs.js script.
module.exports = {
  "437": "cp437",
  "737": "cp737",
  "775": "cp775",
  "850": "cp850",
  "852": "cp852",
  "855": "cp855",
  "856": "cp856",
  "857": "cp857",
  "858": "cp858",
  "860": "cp860",
  "861": "cp861",
  "862": "cp862",
  "863": "cp863",
  "864": "cp864",
  "865": "cp865",
  "866": "cp866",
  "869": "cp869",
  "874": "windows874",
  "922": "cp922",
  "1046": "cp1046",
  "1124": "cp1124",
  "1125": "cp1125",
  "1129": "cp1129",
  "1133": "cp1133",
  "1161": "cp1161",
  "1162": "cp1162",
  "1163": "cp1163",
  "1250": "windows1250",
  "1251": "windows1251",
  "1252": "windows1252",
  "1253": "windows1253",
  "1254": "windows1254",
  "1255": "windows1255",
  "1256": "windows1256",
  "1257": "windows1257",
  "1258": "windows1258",
  "28591": "iso88591",
  "28592": "iso88592",
  "28593": "iso88593",
  "28594": "iso88594",
  "28595": "iso88595",
  "28596": "iso88596",
  "28597": "iso88597",
  "28598": "iso88598",
  "28599": "iso88599",
  "28600": "iso885910",
  "28601": "iso885911",
  "28603": "iso885913",
  "28604": "iso885914",
  "28605": "iso885915",
  "28606": "iso885916",
  "windows874": {
    "type": "_sbcs",
    "chars": "€����…�����������‘’“”•–—�������� กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู����฿เแโใไๅๆ็่้๊๋์ํ๎๏๐๑๒๓๔๕๖๗๘๙๚๛����"
  },
  "win874": "windows874",
  "cp874": "windows874",
  "windows1250": {
    "type": "_sbcs",
    "chars": "€�‚�„…†‡�‰Š‹ŚŤŽŹ�‘’“”•–—�™š›śťžź ˇ˘Ł¤Ą¦§¨©Ş«¬­®Ż°±˛ł´µ¶·¸ąş»Ľ˝ľżŔÁÂĂÄĹĆÇČÉĘËĚÍÎĎĐŃŇÓÔŐÖ×ŘŮÚŰÜÝŢßŕáâăäĺćçčéęëěíîďđńňóôőö÷řůúűüýţ˙"
  },
  "win1250": "windows1250",
  "cp1250": "windows1250",
  "windows1251": {
    "type": "_sbcs",
    "chars": "ЂЃ‚ѓ„…†‡€‰Љ‹ЊЌЋЏђ‘’“”•–—�™љ›њќћџ ЎўЈ¤Ґ¦§Ё©Є«¬­®Ї°±Ііґµ¶·ё№є»јЅѕїАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя"
  },
  "win1251": "windows1251",
  "cp1251": "windows1251",
  "windows1252": {
    "type": "_sbcs",
    "chars": "€�‚ƒ„…†‡ˆ‰Š‹Œ�Ž��‘’“”•–—˜™š›œ�žŸ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿ"
  },
  "win1252": "windows1252",
  "cp1252": "windows1252",
  "windows1253": {
    "type": "_sbcs",
    "chars": "€�‚ƒ„…†‡�‰�‹�����‘’“”•–—�™�›���� ΅Ά£¤¥¦§¨©�«¬­®―°±²³΄µ¶·ΈΉΊ»Ό½ΎΏΐΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡ�ΣΤΥΦΧΨΩΪΫάέήίΰαβγδεζηθικλμνξοπρςστυφχψωϊϋόύώ�"
  },
  "win1253": "windows1253",
  "cp1253": "windows1253",
  "windows1254": {
    "type": "_sbcs",
    "chars": "€�‚ƒ„…†‡ˆ‰Š‹Œ����‘’“”•–—˜™š›œ��Ÿ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏĞÑÒÓÔÕÖ×ØÙÚÛÜİŞßàáâãäåæçèéêëìíîïğñòóôõö÷øùúûüışÿ"
  },
  "win1254": "windows1254",
  "cp1254": "windows1254",
  "windows1255": {
    "type": "_sbcs",
    "chars": "€�‚ƒ„…†‡ˆ‰�‹�����‘’“”•–—˜™�›���� ¡¢£₪¥¦§¨©×«¬­®¯°±²³´µ¶·¸¹÷»¼½¾¿ְֱֲֳִֵֶַָֹֺֻּֽ־ֿ׀ׁׂ׃װױײ׳״�������אבגדהוזחטיךכלםמןנסעףפץצקרשת��‎‏�"
  },
  "win1255": "windows1255",
  "cp1255": "windows1255",
  "windows1256": {
    "type": "_sbcs",
    "chars": "€پ‚ƒ„…†‡ˆ‰ٹ‹Œچژڈگ‘’“”•–—ک™ڑ›œ‌‍ں ،¢£¤¥¦§¨©ھ«¬­®¯°±²³´µ¶·¸¹؛»¼½¾؟ہءآأؤإئابةتثجحخدذرزسشصض×طظعغـفقكàلâمنهوçèéêëىيîïًٌٍَôُِ÷ّùْûü‎‏ے"
  },
  "win1256": "windows1256",
  "cp1256": "windows1256",
  "windows1257": {
    "type": "_sbcs",
    "chars": "€�‚�„…†‡�‰�‹�¨ˇ¸�‘’“”•–—�™�›�¯˛� �¢£¤�¦§Ø©Ŗ«¬­®Æ°±²³´µ¶·ø¹ŗ»¼½¾æĄĮĀĆÄÅĘĒČÉŹĖĢĶĪĻŠŃŅÓŌÕÖ×ŲŁŚŪÜŻŽßąįāćäåęēčéźėģķīļšńņóōõö÷ųłśūüżž˙"
  },
  "win1257": "windows1257",
  "cp1257": "windows1257",
  "windows1258": {
    "type": "_sbcs",
    "chars": "€�‚ƒ„…†‡ˆ‰�‹Œ����‘’“”•–—˜™�›œ��Ÿ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂĂÄÅÆÇÈÉÊË̀ÍÎÏĐÑ̉ÓÔƠÖ×ØÙÚÛÜỮßàáâăäåæçèéêë́íîïđṇ̃óôơö÷øùúûüư₫ÿ"
  },
  "win1258": "windows1258",
  "cp1258": "windows1258",
  "iso88591": {
    "type": "_sbcs",
    "chars": " ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿ"
  },
  "cp28591": "iso88591",
  "iso88592": {
    "type": "_sbcs",
    "chars": " Ą˘Ł¤ĽŚ§¨ŠŞŤŹ­ŽŻ°ą˛ł´ľśˇ¸šşťź˝žżŔÁÂĂÄĹĆÇČÉĘËĚÍÎĎĐŃŇÓÔŐÖ×ŘŮÚŰÜÝŢßŕáâăäĺćçčéęëěíîďđńňóôőö÷řůúűüýţ˙"
  },
  "cp28592": "iso88592",
  "iso88593": {
    "type": "_sbcs",
    "chars": " Ħ˘£¤�Ĥ§¨İŞĞĴ­�Ż°ħ²³´µĥ·¸ışğĵ½�żÀÁÂ�ÄĊĈÇÈÉÊËÌÍÎÏ�ÑÒÓÔĠÖ×ĜÙÚÛÜŬŜßàáâ�äċĉçèéêëìíîï�ñòóôġö÷ĝùúûüŭŝ˙"
  },
  "cp28593": "iso88593",
  "iso88594": {
    "type": "_sbcs",
    "chars": " ĄĸŖ¤ĨĻ§¨ŠĒĢŦ­Ž¯°ą˛ŗ´ĩļˇ¸šēģŧŊžŋĀÁÂÃÄÅÆĮČÉĘËĖÍÎĪĐŅŌĶÔÕÖ×ØŲÚÛÜŨŪßāáâãäåæįčéęëėíîīđņōķôõö÷øųúûüũū˙"
  },
  "cp28594": "iso88594",
  "iso88595": {
    "type": "_sbcs",
    "chars": " ЁЂЃЄЅІЇЈЉЊЋЌ­ЎЏАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя№ёђѓєѕіїјљњћќ§ўџ"
  },
  "cp28595": "iso88595",
  "iso88596": {
    "type": "_sbcs",
    "chars": " ���¤�������،­�������������؛���؟�ءآأؤإئابةتثجحخدذرزسشصضطظعغ�����ـفقكلمنهوىيًٌٍَُِّْ�������������"
  },
  "cp28596": "iso88596",
  "iso88597": {
    "type": "_sbcs",
    "chars": " ‘’£€₯¦§¨©ͺ«¬­�―°±²³΄΅Ά·ΈΉΊ»Ό½ΎΏΐΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡ�ΣΤΥΦΧΨΩΪΫάέήίΰαβγδεζηθικλμνξοπρςστυφχψωϊϋόύώ�"
  },
  "cp28597": "iso88597",
  "iso88598": {
    "type": "_sbcs",
    "chars": " �¢£¤¥¦§¨©×«¬­®¯°±²³´µ¶·¸¹÷»¼½¾��������������������������������‗אבגדהוזחטיךכלםמןנסעףפץצקרשת��‎‏�"
  },
  "cp28598": "iso88598",
  "iso88599": {
    "type": "_sbcs",
    "chars": " ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏĞÑÒÓÔÕÖ×ØÙÚÛÜİŞßàáâãäåæçèéêëìíîïğñòóôõö÷øùúûüışÿ"
  },
  "cp28599": "iso88599",
  "iso885910": {
    "type": "_sbcs",
    "chars": " ĄĒĢĪĨĶ§ĻĐŠŦŽ­ŪŊ°ąēģīĩķ·ļđšŧž―ūŋĀÁÂÃÄÅÆĮČÉĘËĖÍÎÏÐŅŌÓÔÕÖŨØŲÚÛÜÝÞßāáâãäåæįčéęëėíîïðņōóôõöũøųúûüýþĸ"
  },
  "cp28600": "iso885910",
  "iso885911": {
    "type": "_sbcs",
    "chars": " กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู����฿เแโใไๅๆ็่้๊๋์ํ๎๏๐๑๒๓๔๕๖๗๘๙๚๛����"
  },
  "cp28601": "iso885911",
  "iso885913": {
    "type": "_sbcs",
    "chars": " ”¢£¤„¦§Ø©Ŗ«¬­®Æ°±²³“µ¶·ø¹ŗ»¼½¾æĄĮĀĆÄÅĘĒČÉŹĖĢĶĪĻŠŃŅÓŌÕÖ×ŲŁŚŪÜŻŽßąįāćäåęēčéźėģķīļšńņóōõö÷ųłśūüżž’"
  },
  "cp28603": "iso885913",
  "iso885914": {
    "type": "_sbcs",
    "chars": " Ḃḃ£ĊċḊ§Ẁ©ẂḋỲ­®ŸḞḟĠġṀṁ¶ṖẁṗẃṠỳẄẅṡÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏŴÑÒÓÔÕÖṪØÙÚÛÜÝŶßàáâãäåæçèéêëìíîïŵñòóôõöṫøùúûüýŷÿ"
  },
  "cp28604": "iso885914",
  "iso885915": {
    "type": "_sbcs",
    "chars": " ¡¢£€¥Š§š©ª«¬­®¯°±²³Žµ¶·ž¹º»ŒœŸ¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿ"
  },
  "cp28605": "iso885915",
  "iso885916": {
    "type": "_sbcs",
    "chars": " ĄąŁ€„Š§š©Ș«Ź­źŻ°±ČłŽ”¶·žčș»ŒœŸżÀÁÂĂÄĆÆÇÈÉÊËÌÍÎÏĐŃÒÓÔŐÖŚŰÙÚÛÜĘȚßàáâăäćæçèéêëìíîïđńòóôőöśűùúûüęțÿ"
  },
  "cp28606": "iso885916",
  "cp437": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm437": "cp437",
  "csibm437": "cp437",
  "cp737": {
    "type": "_sbcs",
    "chars": "ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩαβγδεζηθικλμνξοπρσςτυφχψ░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀ωάέήϊίόύϋώΆΈΉΊΌΎΏ±≥≤ΪΫ÷≈°∙·√ⁿ²■ "
  },
  "ibm737": "cp737",
  "csibm737": "cp737",
  "cp775": {
    "type": "_sbcs",
    "chars": "ĆüéāäģåćłēŖŗīŹÄÅÉæÆōöĢ¢ŚśÖÜø£Ø×¤ĀĪóŻżź”¦©®¬½¼Ł«»░▒▓│┤ĄČĘĖ╣║╗╝ĮŠ┐└┴┬├─┼ŲŪ╚╔╩╦╠═╬Žąčęėįšųūž┘┌█▄▌▐▀ÓßŌŃõÕµńĶķĻļņĒŅ’­±“¾¶§÷„°∙·¹³²■ "
  },
  "ibm775": "cp775",
  "csibm775": "cp775",
  "cp850": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜø£Ø×ƒáíóúñÑªº¿®¬½¼¡«»░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐└┴┬├─┼ãÃ╚╔╩╦╠═╬¤ðÐÊËÈıÍÎÏ┘┌█▄¦Ì▀ÓßÔÒõÕµþÞÚÛÙýÝ¯´­±‗¾¶§÷¸°¨·¹³²■ "
  },
  "ibm850": "cp850",
  "csibm850": "cp850",
  "cp852": {
    "type": "_sbcs",
    "chars": "ÇüéâäůćçłëŐőîŹÄĆÉĹĺôöĽľŚśÖÜŤťŁ×čáíóúĄąŽžĘę¬źČş«»░▒▓│┤ÁÂĚŞ╣║╗╝Żż┐└┴┬├─┼Ăă╚╔╩╦╠═╬¤đĐĎËďŇÍÎě┘┌█▄ŢŮ▀ÓßÔŃńňŠšŔÚŕŰýÝţ´­˝˛ˇ˘§÷¸°¨˙űŘř■ "
  },
  "ibm852": "cp852",
  "csibm852": "cp852",
  "cp855": {
    "type": "_sbcs",
    "chars": "ђЂѓЃёЁєЄѕЅіІїЇјЈљЉњЊћЋќЌўЎџЏюЮъЪаАбБцЦдДеЕфФгГ«»░▒▓│┤хХиИ╣║╗╝йЙ┐└┴┬├─┼кК╚╔╩╦╠═╬¤лЛмМнНоОп┘┌█▄Пя▀ЯрРсСтТуУжЖвВьЬ№­ыЫзЗшШэЭщЩчЧ§■ "
  },
  "ibm855": "cp855",
  "csibm855": "cp855",
  "cp856": {
    "type": "_sbcs",
    "chars": "אבגדהוזחטיךכלםמןנסעףפץצקרשת�£�×����������®¬½¼�«»░▒▓│┤���©╣║╗╝¢¥┐└┴┬├─┼��╚╔╩╦╠═╬¤���������┘┌█▄¦�▀������µ�������¯´­±‗¾¶§÷¸°¨·¹³²■ "
  },
  "ibm856": "cp856",
  "csibm856": "cp856",
  "cp857": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèïîıÄÅÉæÆôöòûùİÖÜø£ØŞşáíóúñÑĞğ¿®¬½¼¡«»░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐└┴┬├─┼ãÃ╚╔╩╦╠═╬¤ºªÊËÈ�ÍÎÏ┘┌█▄¦Ì▀ÓßÔÒõÕµ�×ÚÛÙìÿ¯´­±�¾¶§÷¸°¨·¹³²■ "
  },
  "ibm857": "cp857",
  "csibm857": "cp857",
  "cp858": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜø£Ø×ƒáíóúñÑªº¿®¬½¼¡«»░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐└┴┬├─┼ãÃ╚╔╩╦╠═╬¤ðÐÊËÈ€ÍÎÏ┘┌█▄¦Ì▀ÓßÔÒõÕµþÞÚÛÙýÝ¯´­±‗¾¶§÷¸°¨·¹³²■ "
  },
  "ibm858": "cp858",
  "csibm858": "cp858",
  "cp860": {
    "type": "_sbcs",
    "chars": "ÇüéâãàÁçêÊèÍÔìÃÂÉÀÈôõòÚùÌÕÜ¢£Ù₧ÓáíóúñÑªº¿Ò¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm860": "cp860",
  "csibm860": "cp860",
  "cp861": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèÐðÞÄÅÉæÆôöþûÝýÖÜø£Ø₧ƒáíóúÁÍÓÚ¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm861": "cp861",
  "csibm861": "cp861",
  "cp862": {
    "type": "_sbcs",
    "chars": "אבגדהוזחטיךכלםמןנסעףפץצקרשת¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm862": "cp862",
  "csibm862": "cp862",
  "cp863": {
    "type": "_sbcs",
    "chars": "ÇüéâÂà¶çêëèïî‗À§ÉÈÊôËÏûù¤ÔÜ¢£ÙÛƒ¦´óú¨¸³¯Î⌐¬½¼¾«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm863": "cp863",
  "csibm863": "cp863",
  "cp864": {
    "type": "_sbcs",
    "chars": "\u0000\u0001\u0002\u0003\u0004\u0005\u0006\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010\u0011\u0012\u0013\u0014\u0015\u0016\u0017\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f !\"#$٪&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~°·∙√▒─│┼┤┬├┴┐┌└┘β∞φ±½¼≈«»ﻷﻸ��ﻻﻼ� ­ﺂ£¤ﺄ��ﺎﺏﺕﺙ،ﺝﺡﺥ٠١٢٣٤٥٦٧٨٩ﻑ؛ﺱﺵﺹ؟¢ﺀﺁﺃﺅﻊﺋﺍﺑﺓﺗﺛﺟﺣﺧﺩﺫﺭﺯﺳﺷﺻﺿﻁﻅﻋﻏ¦¬÷×ﻉـﻓﻗﻛﻟﻣﻧﻫﻭﻯﻳﺽﻌﻎﻍﻡﹽّﻥﻩﻬﻰﻲﻐﻕﻵﻶﻝﻙﻱ■�"
  },
  "ibm864": "cp864",
  "csibm864": "cp864",
  "cp865": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜø£Ø₧ƒáíóúñÑªº¿⌐¬½¼¡«¤░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm865": "cp865",
  "csibm865": "cp865",
  "cp866": {
    "type": "_sbcs",
    "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмноп░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀рстуфхцчшщъыьэюяЁёЄєЇїЎў°∙·√№¤■ "
  },
  "ibm866": "cp866",
  "csibm866": "cp866",
  "cp869": {
    "type": "_sbcs",
    "chars": "������Ά�·¬¦‘’Έ―ΉΊΪΌ��ΎΫ©Ώ²³ά£έήίϊΐόύΑΒΓΔΕΖΗ½ΘΙ«»░▒▓│┤ΚΛΜΝ╣║╗╝ΞΟ┐└┴┬├─┼ΠΡ╚╔╩╦╠═╬ΣΤΥΦΧΨΩαβγ┘┌█▄δε▀ζηθικλμνξοπρσςτ΄­±υφχ§ψ΅°¨ωϋΰώ■ "
  },
  "ibm869": "cp869",
  "csibm869": "cp869",
  "cp922": {
    "type": "_sbcs",
    "chars": " ¡¢£¤¥¦§¨©ª«¬­®‾°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏŠÑÒÓÔÕÖ×ØÙÚÛÜÝŽßàáâãäåæçèéêëìíîïšñòóôõö÷øùúûüýžÿ"
  },
  "ibm922": "cp922",
  "csibm922": "cp922",
  "cp1046": {
    "type": "_sbcs",
    "chars": "ﺈ×÷ﹱ■│─┐┌└┘ﹹﹻﹽﹿﹷﺊﻰﻳﻲﻎﻏﻐﻶﻸﻺﻼ ¤ﺋﺑﺗﺛﺟﺣ،­ﺧﺳ٠١٢٣٤٥٦٧٨٩ﺷ؛ﺻﺿﻊ؟ﻋءآأؤإئابةتثجحخدذرزسشصضطﻇعغﻌﺂﺄﺎﻓـفقكلمنهوىيًٌٍَُِّْﻗﻛﻟﻵﻷﻹﻻﻣﻧﻬﻩ�"
  },
  "ibm1046": "cp1046",
  "csibm1046": "cp1046",
  "cp1124": {
    "type": "_sbcs",
    "chars": " ЁЂҐЄЅІЇЈЉЊЋЌ­ЎЏАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя№ёђґєѕіїјљњћќ§ўџ"
  },
  "ibm1124": "cp1124",
  "csibm1124": "cp1124",
  "cp1125": {
    "type": "_sbcs",
    "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмноп░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀рстуфхцчшщъыьэюяЁёҐґЄєІіЇї·√№¤■ "
  },
  "ibm1125": "cp1125",
  "csibm1125": "cp1125",
  "cp1129": {
    "type": "_sbcs",
    "chars": " ¡¢£¤¥¦§œ©ª«¬­®¯°±²³Ÿµ¶·Œ¹º»¼½¾¿ÀÁÂĂÄÅÆÇÈÉÊË̀ÍÎÏĐÑ̉ÓÔƠÖ×ØÙÚÛÜỮßàáâăäåæçèéêë́íîïđṇ̃óôơö÷øùúûüư₫ÿ"
  },
  "ibm1129": "cp1129",
  "csibm1129": "cp1129",
  "cp1133": {
    "type": "_sbcs",
    "chars": " ກຂຄງຈສຊຍດຕຖທນບປຜຝພຟມຢຣລວຫອຮ���ຯະາຳິີຶືຸູຼັົຽ���ເແໂໃໄ່້໊໋໌ໍໆ�ໜໝ₭����������������໐໑໒໓໔໕໖໗໘໙��¢¬¦�"
  },
  "ibm1133": "cp1133",
  "csibm1133": "cp1133",
  "cp1161": {
    "type": "_sbcs",
    "chars": "��������������������������������่กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู้๊๋€฿เแโใไๅๆ็่้๊๋์ํ๎๏๐๑๒๓๔๕๖๗๘๙๚๛¢¬¦ "
  },
  "ibm1161": "cp1161",
  "csibm1161": "cp1161",
  "cp1162": {
    "type": "_sbcs",
    "chars": "€…‘’“”•–— กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู����฿เแโใไๅๆ็่้๊๋์ํ๎๏๐๑๒๓๔๕๖๗๘๙๚๛����"
  },
  "ibm1162": "cp1162",
  "csibm1162": "cp1162",
  "cp1163": {
    "type": "_sbcs",
    "chars": " ¡¢£€¥¦§œ©ª«¬­®¯°±²³Ÿµ¶·Œ¹º»¼½¾¿ÀÁÂĂÄÅÆÇÈÉÊË̀ÍÎÏĐÑ̉ÓÔƠÖ×ØÙÚÛÜỮßàáâăäåæçèéêë́íîïđṇ̃óôơö÷øùúûüư₫ÿ"
  },
  "ibm1163": "cp1163",
  "csibm1163": "cp1163",
  "maccroatian": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®Š™´¨≠ŽØ∞±≤≥∆µ∂∑∏š∫ªºΩžø¿¡¬√ƒ≈Ć«Č… ÀÃÕŒœĐ—“”‘’÷◊�©⁄¤‹›Æ»–·‚„‰ÂćÁčÈÍÎÏÌÓÔđÒÚÛÙıˆ˜¯πË˚¸Êæˇ"
  },
  "maccyrillic": {
    "type": "_sbcs",
    "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ†°¢£§•¶І®©™Ђђ≠Ѓѓ∞±≤≥іµ∂ЈЄєЇїЉљЊњјЅ¬√ƒ≈∆«»… ЋћЌќѕ–—“”‘’÷„ЎўЏџ№Ёёяабвгдежзийклмнопрстуфхцчшщъыьэю¤"
  },
  "macgreek": {
    "type": "_sbcs",
    "chars": "Ä¹²É³ÖÜ΅àâä΄¨çéèêë£™îï•½‰ôö¦­ùûü†ΓΔΘΛΞΠß®©ΣΪ§≠°·Α±≤≥¥ΒΕΖΗΙΚΜΦΫΨΩάΝ¬ΟΡ≈Τ«»… ΥΧΆΈœ–―“”‘’÷ΉΊΌΎέήίόΏύαβψδεφγηιξκλμνοπώρστθωςχυζϊϋΐΰ�"
  },
  "maciceland": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûüÝ°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄¤ÐðÞþý·‚„‰ÂÊÁËÈÍÎÏÌÓÔ�ÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ"
  },
  "macroman": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄¤‹›ﬁﬂ‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔ�ÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ"
  },
  "macromania": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ĂŞ∞±≤≥¥µ∂∑∏π∫ªºΩăş¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄¤‹›Ţţ‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔ�ÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ"
  },
  "macthai": {
    "type": "_sbcs",
    "chars": "«»…“”�•‘’� กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู﻿​–—฿เแโใไๅๆ็่้๊๋์ํ™๏๐๑๒๓๔๕๖๗๘๙®©����"
  },
  "macturkish": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸĞğİıŞş‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔ�ÒÚÛÙ�ˆ˜¯˘˙˚¸˝˛ˇ"
  },
  "macukraine": {
    "type": "_sbcs",
    "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ†°Ґ£§•¶І®©™Ђђ≠Ѓѓ∞±≤≥іµґЈЄєЇїЉљЊњјЅ¬√ƒ≈∆«»… ЋћЌќѕ–—“”‘’÷„ЎўЏџ№Ёёяабвгдежзийклмнопрстуфхцчшщъыьэю¤"
  },
  "koi8r": {
    "type": "_sbcs",
    "chars": "─│┌┐└┘├┤┬┴┼▀▄█▌▐░▒▓⌠■∙√≈≤≥ ⌡°²·÷═║╒ё╓╔╕╖╗╘╙╚╛╜╝╞╟╠╡Ё╢╣╤╥╦╧╨╩╪╫╬©юабцдефгхийклмнопярстужвьызшэщчъЮАБЦДЕФГХИЙКЛМНОПЯРСТУЖВЬЫЗШЭЩЧЪ"
  },
  "koi8u": {
    "type": "_sbcs",
    "chars": "─│┌┐└┘├┤┬┴┼▀▄█▌▐░▒▓⌠■∙√≈≤≥ ⌡°²·÷═║╒ёє╔ії╗╘╙╚╛ґ╝╞╟╠╡ЁЄ╣ІЇ╦╧╨╩╪Ґ╬©юабцдефгхийклмнопярстужвьызшэщчъЮАБЦДЕФГХИЙКЛМНОПЯРСТУЖВЬЫЗШЭЩЧЪ"
  },
  "koi8ru": {
    "type": "_sbcs",
    "chars": "─│┌┐└┘├┤┬┴┼▀▄█▌▐░▒▓⌠■∙√≈≤≥ ⌡°²·÷═║╒ёє╔ії╗╘╙╚╛ґў╞╟╠╡ЁЄ╣ІЇ╦╧╨╩╪ҐЎ©юабцдефгхийклмнопярстужвьызшэщчъЮАБЦДЕФГХИЙКЛМНОПЯРСТУЖВЬЫЗШЭЩЧЪ"
  },
  "koi8t": {
    "type": "_sbcs",
    "chars": "қғ‚Ғ„…†‡�‰ҳ‹ҲҷҶ�Қ‘’“”•–—�™�›�����ӯӮё¤ӣ¦§���«¬­®�°±²Ё�Ӣ¶·�№�»���©юабцдефгхийклмнопярстужвьызшэщчъЮАБЦДЕФГХИЙКЛМНОПЯРСТУЖВЬЫЗШЭЩЧЪ"
  },
  "armscii8": {
    "type": "_sbcs",
    "chars": " �և։)(»«—.՝,-֊…՜՛՞ԱաԲբԳգԴդԵեԶզԷէԸըԹթԺժԻիԼլԽխԾծԿկՀհՁձՂղՃճՄմՅյՆնՇշՈոՉչՊպՋջՌռՍսՎվՏտՐրՑցՒւՓփՔքՕօՖֆ՚�"
  },
  "rk1048": {
    "type": "_sbcs",
    "chars": "ЂЃ‚ѓ„…†‡€‰Љ‹ЊҚҺЏђ‘’“”•–—�™љ›њқһџ ҰұӘ¤Ө¦§Ё©Ғ«¬­®Ү°±Ііөµ¶·ё№ғ»әҢңүАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя"
  },
  "tcvn": {
    "type": "_sbcs",
    "chars": "\u0000ÚỤ\u0003ỪỬỮ\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010ỨỰỲỶỸÝỴ\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f !\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~ÀẢÃÁẠẶẬÈẺẼÉẸỆÌỈĨÍỊÒỎÕÓỌỘỜỞỠỚỢÙỦŨ ĂÂÊÔƠƯĐăâêôơưđẶ̀̀̉̃́àảãáạẲằẳẵắẴẮẦẨẪẤỀặầẩẫấậèỂẻẽéẹềểễếệìỉỄẾỒĩíịòỔỏõóọồổỗốộờởỡớợùỖủũúụừửữứựỳỷỹýỵỐ"
  },
  "georgianacademy": {
    "type": "_sbcs",
    "chars": "‚ƒ„…†‡ˆ‰Š‹Œ‘’“”•–—˜™š›œŸ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿აბგდევზთიკლმნოპჟრსტუფქღყშჩცძწჭხჯჰჱჲჳჴჵჶçèéêëìíîïðñòóôõö÷øùúûüýþÿ"
  },
  "georgianps": {
    "type": "_sbcs",
    "chars": "‚ƒ„…†‡ˆ‰Š‹Œ‘’“”•–—˜™š›œŸ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿აბგდევზჱთიკლმნჲოპჟრსტჳუფქღყშჩცძწჭხჴჯჰჵæçèéêëìíîïðñòóôõö÷øùúûüýþÿ"
  },
  "pt154": {
    "type": "_sbcs",
    "chars": "ҖҒӮғ„…ҶҮҲүҠӢҢҚҺҸҗ‘’“”•–—ҳҷҡӣңқһҹ ЎўЈӨҘҰ§Ё©Ә«¬ӯ®Ҝ°ұІіҙө¶·ё№ә»јҪҫҝАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя"
  },
  "viscii": {
    "type": "_sbcs",
    "chars": "\u0000\u0001Ẳ\u0003\u0004ẴẪ\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010\u0011\u0012\u0013Ỷ\u0015\u0016\u0017\u0018Ỹ\u001a\u001b\u001c\u001dỴ\u001f !\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~ẠẮẰẶẤẦẨẬẼẸẾỀỂỄỆỐỒỔỖỘỢỚỜỞỊỎỌỈỦŨỤỲÕắằặấầẩậẽẹếềểễệốồổỗỠƠộờởịỰỨỪỬơớƯÀÁÂÃẢĂẳẵÈÉÊẺÌÍĨỳĐứÒÓÔạỷừửÙÚỹỵÝỡưàáâãảăữẫèéêẻìíĩỉđựòóôõỏọụùúũủýợỮ"
  },
  "iso646cn": {
    "type": "_sbcs",
    "chars": "\u0000\u0001\u0002\u0003\u0004\u0005\u0006\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010\u0011\u0012\u0013\u0014\u0015\u0016\u0017\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f !\"#¥%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}‾��������������������������������������������������������������������������������������������������������������������������������"
  },
  "iso646jp": {
    "type": "_sbcs",
    "chars": "\u0000\u0001\u0002\u0003\u0004\u0005\u0006\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010\u0011\u0012\u0013\u0014\u0015\u0016\u0017\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f !\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[¥]^_`abcdefghijklmnopqrstuvwxyz{|}‾��������������������������������������������������������������������������������������������������������������������������������"
  },
  "hproman8": {
    "type": "_sbcs",
    "chars": " ÀÂÈÊËÎÏ´ˋˆ¨˜ÙÛ₤¯Ýý°ÇçÑñ¡¿¤£¥§ƒ¢âêôûáéóúàèòùäëöüÅîØÆåíøæÄìÖÜÉïßÔÁÃãÐðÍÌÓÒÕõŠšÚŸÿÞþ·µ¶¾—¼½ªº«■»±�"
  },
  "macintosh": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄¤‹›ﬁﬂ‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔ�ÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ"
  },
  "ascii": {
    "type": "_sbcs",
    "chars": "��������������������������������������������������������������������������������������������������������������������������������"
  },
  "tis620": {
    "type": "_sbcs",
    "chars": "���������������������������������กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู����฿เแโใไๅๆ็่้๊๋์ํ๎๏๐๑๒๓๔๕๖๗๘๙๚๛����"
  }
}

/***/ }),
/* 398 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var Buffer = __webpack_require__(39).Buffer;

// Multibyte codec. In this scheme, a character is represented by 1 or more bytes.
// Our codec supports UTF-16 surrogates, extensions for GB18030 and unicode sequences.
// To save memory and loading time, we read table files only when requested.

exports._dbcs = DBCSCodec;

var UNASSIGNED = -1,
    GB18030_CODE = -2,
    SEQ_START  = -10,
    NODE_START = -1000,
    UNASSIGNED_NODE = new Array(0x100),
    DEF_CHAR = -1;

for (var i = 0; i < 0x100; i++)
    UNASSIGNED_NODE[i] = UNASSIGNED;


// Class DBCSCodec reads and initializes mapping tables.
function DBCSCodec(codecOptions, iconv) {
    this.encodingName = codecOptions.encodingName;
    if (!codecOptions)
        throw new Error("DBCS codec is called without the data.")
    if (!codecOptions.table)
        throw new Error("Encoding '" + this.encodingName + "' has no data.");

    // Load tables.
    var mappingTable = codecOptions.table();


    // Decode tables: MBCS -> Unicode.

    // decodeTables is a trie, encoded as an array of arrays of integers. Internal arrays are trie nodes and all have len = 256.
    // Trie root is decodeTables[0].
    // Values: >=  0 -> unicode character code. can be > 0xFFFF
    //         == UNASSIGNED -> unknown/unassigned sequence.
    //         == GB18030_CODE -> this is the end of a GB18030 4-byte sequence.
    //         <= NODE_START -> index of the next node in our trie to process next byte.
    //         <= SEQ_START  -> index of the start of a character code sequence, in decodeTableSeq.
    this.decodeTables = [];
    this.decodeTables[0] = UNASSIGNED_NODE.slice(0); // Create root node.

    // Sometimes a MBCS char corresponds to a sequence of unicode chars. We store them as arrays of integers here. 
    this.decodeTableSeq = [];

    // Actual mapping tables consist of chunks. Use them to fill up decode tables.
    for (var i = 0; i < mappingTable.length; i++)
        this._addDecodeChunk(mappingTable[i]);

    // Load & create GB18030 tables when needed.
    if (typeof codecOptions.gb18030 === 'function') {
        this.gb18030 = codecOptions.gb18030(); // Load GB18030 ranges.

        // Add GB18030 common decode nodes.
        var commonThirdByteNodeIdx = this.decodeTables.length;
        this.decodeTables.push(UNASSIGNED_NODE.slice(0));

        var commonFourthByteNodeIdx = this.decodeTables.length;
        this.decodeTables.push(UNASSIGNED_NODE.slice(0));

        // Fill out the tree
        var firstByteNode = this.decodeTables[0];
        for (var i = 0x81; i <= 0xFE; i++) {
            var secondByteNode = this.decodeTables[NODE_START - firstByteNode[i]];
            for (var j = 0x30; j <= 0x39; j++) {
                if (secondByteNode[j] === UNASSIGNED) {
                    secondByteNode[j] = NODE_START - commonThirdByteNodeIdx;
                } else if (secondByteNode[j] > NODE_START) {
                    throw new Error("gb18030 decode tables conflict at byte 2");
                }

                var thirdByteNode = this.decodeTables[NODE_START - secondByteNode[j]];
                for (var k = 0x81; k <= 0xFE; k++) {
                    if (thirdByteNode[k] === UNASSIGNED) {
                        thirdByteNode[k] = NODE_START - commonFourthByteNodeIdx;
                    } else if (thirdByteNode[k] === NODE_START - commonFourthByteNodeIdx) {
                        continue;
                    } else if (thirdByteNode[k] > NODE_START) {
                        throw new Error("gb18030 decode tables conflict at byte 3");
                    }

                    var fourthByteNode = this.decodeTables[NODE_START - thirdByteNode[k]];
                    for (var l = 0x30; l <= 0x39; l++) {
                        if (fourthByteNode[l] === UNASSIGNED)
                            fourthByteNode[l] = GB18030_CODE;
                    }
                }
            }
        }
    }

    this.defaultCharUnicode = iconv.defaultCharUnicode;

    
    // Encode tables: Unicode -> DBCS.

    // `encodeTable` is array mapping from unicode char to encoded char. All its values are integers for performance.
    // Because it can be sparse, it is represented as array of buckets by 256 chars each. Bucket can be null.
    // Values: >=  0 -> it is a normal char. Write the value (if <=256 then 1 byte, if <=65536 then 2 bytes, etc.).
    //         == UNASSIGNED -> no conversion found. Output a default char.
    //         <= SEQ_START  -> it's an index in encodeTableSeq, see below. The character starts a sequence.
    this.encodeTable = [];
    
    // `encodeTableSeq` is used when a sequence of unicode characters is encoded as a single code. We use a tree of
    // objects where keys correspond to characters in sequence and leafs are the encoded dbcs values. A special DEF_CHAR key
    // means end of sequence (needed when one sequence is a strict subsequence of another).
    // Objects are kept separately from encodeTable to increase performance.
    this.encodeTableSeq = [];

    // Some chars can be decoded, but need not be encoded.
    var skipEncodeChars = {};
    if (codecOptions.encodeSkipVals)
        for (var i = 0; i < codecOptions.encodeSkipVals.length; i++) {
            var val = codecOptions.encodeSkipVals[i];
            if (typeof val === 'number')
                skipEncodeChars[val] = true;
            else
                for (var j = val.from; j <= val.to; j++)
                    skipEncodeChars[j] = true;
        }
        
    // Use decode trie to recursively fill out encode tables.
    this._fillEncodeTable(0, 0, skipEncodeChars);

    // Add more encoding pairs when needed.
    if (codecOptions.encodeAdd) {
        for (var uChar in codecOptions.encodeAdd)
            if (Object.prototype.hasOwnProperty.call(codecOptions.encodeAdd, uChar))
                this._setEncodeChar(uChar.charCodeAt(0), codecOptions.encodeAdd[uChar]);
    }

    this.defCharSB  = this.encodeTable[0][iconv.defaultCharSingleByte.charCodeAt(0)];
    if (this.defCharSB === UNASSIGNED) this.defCharSB = this.encodeTable[0]['?'];
    if (this.defCharSB === UNASSIGNED) this.defCharSB = "?".charCodeAt(0);
}

DBCSCodec.prototype.encoder = DBCSEncoder;
DBCSCodec.prototype.decoder = DBCSDecoder;

// Decoder helpers
DBCSCodec.prototype._getDecodeTrieNode = function(addr) {
    var bytes = [];
    for (; addr > 0; addr >>>= 8)
        bytes.push(addr & 0xFF);
    if (bytes.length == 0)
        bytes.push(0);

    var node = this.decodeTables[0];
    for (var i = bytes.length-1; i > 0; i--) { // Traverse nodes deeper into the trie.
        var val = node[bytes[i]];

        if (val == UNASSIGNED) { // Create new node.
            node[bytes[i]] = NODE_START - this.decodeTables.length;
            this.decodeTables.push(node = UNASSIGNED_NODE.slice(0));
        }
        else if (val <= NODE_START) { // Existing node.
            node = this.decodeTables[NODE_START - val];
        }
        else
            throw new Error("Overwrite byte in " + this.encodingName + ", addr: " + addr.toString(16));
    }
    return node;
}


DBCSCodec.prototype._addDecodeChunk = function(chunk) {
    // First element of chunk is the hex mbcs code where we start.
    var curAddr = parseInt(chunk[0], 16);

    // Choose the decoding node where we'll write our chars.
    var writeTable = this._getDecodeTrieNode(curAddr);
    curAddr = curAddr & 0xFF;

    // Write all other elements of the chunk to the table.
    for (var k = 1; k < chunk.length; k++) {
        var part = chunk[k];
        if (typeof part === "string") { // String, write as-is.
            for (var l = 0; l < part.length;) {
                var code = part.charCodeAt(l++);
                if (0xD800 <= code && code < 0xDC00) { // Decode surrogate
                    var codeTrail = part.charCodeAt(l++);
                    if (0xDC00 <= codeTrail && codeTrail < 0xE000)
                        writeTable[curAddr++] = 0x10000 + (code - 0xD800) * 0x400 + (codeTrail - 0xDC00);
                    else
                        throw new Error("Incorrect surrogate pair in "  + this.encodingName + " at chunk " + chunk[0]);
                }
                else if (0x0FF0 < code && code <= 0x0FFF) { // Character sequence (our own encoding used)
                    var len = 0xFFF - code + 2;
                    var seq = [];
                    for (var m = 0; m < len; m++)
                        seq.push(part.charCodeAt(l++)); // Simple variation: don't support surrogates or subsequences in seq.

                    writeTable[curAddr++] = SEQ_START - this.decodeTableSeq.length;
                    this.decodeTableSeq.push(seq);
                }
                else
                    writeTable[curAddr++] = code; // Basic char
            }
        } 
        else if (typeof part === "number") { // Integer, meaning increasing sequence starting with prev character.
            var charCode = writeTable[curAddr - 1] + 1;
            for (var l = 0; l < part; l++)
                writeTable[curAddr++] = charCode++;
        }
        else
            throw new Error("Incorrect type '" + typeof part + "' given in "  + this.encodingName + " at chunk " + chunk[0]);
    }
    if (curAddr > 0xFF)
        throw new Error("Incorrect chunk in "  + this.encodingName + " at addr " + chunk[0] + ": too long" + curAddr);
}

// Encoder helpers
DBCSCodec.prototype._getEncodeBucket = function(uCode) {
    var high = uCode >> 8; // This could be > 0xFF because of astral characters.
    if (this.encodeTable[high] === undefined)
        this.encodeTable[high] = UNASSIGNED_NODE.slice(0); // Create bucket on demand.
    return this.encodeTable[high];
}

DBCSCodec.prototype._setEncodeChar = function(uCode, dbcsCode) {
    var bucket = this._getEncodeBucket(uCode);
    var low = uCode & 0xFF;
    if (bucket[low] <= SEQ_START)
        this.encodeTableSeq[SEQ_START-bucket[low]][DEF_CHAR] = dbcsCode; // There's already a sequence, set a single-char subsequence of it.
    else if (bucket[low] == UNASSIGNED)
        bucket[low] = dbcsCode;
}

DBCSCodec.prototype._setEncodeSequence = function(seq, dbcsCode) {
    
    // Get the root of character tree according to first character of the sequence.
    var uCode = seq[0];
    var bucket = this._getEncodeBucket(uCode);
    var low = uCode & 0xFF;

    var node;
    if (bucket[low] <= SEQ_START) {
        // There's already a sequence with  - use it.
        node = this.encodeTableSeq[SEQ_START-bucket[low]];
    }
    else {
        // There was no sequence object - allocate a new one.
        node = {};
        if (bucket[low] !== UNASSIGNED) node[DEF_CHAR] = bucket[low]; // If a char was set before - make it a single-char subsequence.
        bucket[low] = SEQ_START - this.encodeTableSeq.length;
        this.encodeTableSeq.push(node);
    }

    // Traverse the character tree, allocating new nodes as needed.
    for (var j = 1; j < seq.length-1; j++) {
        var oldVal = node[uCode];
        if (typeof oldVal === 'object')
            node = oldVal;
        else {
            node = node[uCode] = {}
            if (oldVal !== undefined)
                node[DEF_CHAR] = oldVal
        }
    }

    // Set the leaf to given dbcsCode.
    uCode = seq[seq.length-1];
    node[uCode] = dbcsCode;
}

DBCSCodec.prototype._fillEncodeTable = function(nodeIdx, prefix, skipEncodeChars) {
    var node = this.decodeTables[nodeIdx];
    var hasValues = false;
    var subNodeEmpty = {};
    for (var i = 0; i < 0x100; i++) {
        var uCode = node[i];
        var mbCode = prefix + i;
        if (skipEncodeChars[mbCode])
            continue;

        if (uCode >= 0) {
            this._setEncodeChar(uCode, mbCode);
            hasValues = true;
        } else if (uCode <= NODE_START) {
            var subNodeIdx = NODE_START - uCode;
            if (!subNodeEmpty[subNodeIdx]) {  // Skip empty subtrees (they are too large in gb18030).
                var newPrefix = (mbCode << 8) >>> 0;  // NOTE: '>>> 0' keeps 32-bit num positive.
                if (this._fillEncodeTable(subNodeIdx, newPrefix, skipEncodeChars))
                    hasValues = true;
                else
                    subNodeEmpty[subNodeIdx] = true;
            }
        } else if (uCode <= SEQ_START) {
            this._setEncodeSequence(this.decodeTableSeq[SEQ_START - uCode], mbCode);
            hasValues = true;
        }
    }
    return hasValues;
}



// == Encoder ==================================================================

function DBCSEncoder(options, codec) {
    // Encoder state
    this.leadSurrogate = -1;
    this.seqObj = undefined;
    
    // Static data
    this.encodeTable = codec.encodeTable;
    this.encodeTableSeq = codec.encodeTableSeq;
    this.defaultCharSingleByte = codec.defCharSB;
    this.gb18030 = codec.gb18030;
}

DBCSEncoder.prototype.write = function(str) {
    var newBuf = Buffer.alloc(str.length * (this.gb18030 ? 4 : 3)),
        leadSurrogate = this.leadSurrogate,
        seqObj = this.seqObj, nextChar = -1,
        i = 0, j = 0;

    while (true) {
        // 0. Get next character.
        if (nextChar === -1) {
            if (i == str.length) break;
            var uCode = str.charCodeAt(i++);
        }
        else {
            var uCode = nextChar;
            nextChar = -1;    
        }

        // 1. Handle surrogates.
        if (0xD800 <= uCode && uCode < 0xE000) { // Char is one of surrogates.
            if (uCode < 0xDC00) { // We've got lead surrogate.
                if (leadSurrogate === -1) {
                    leadSurrogate = uCode;
                    continue;
                } else {
                    leadSurrogate = uCode;
                    // Double lead surrogate found.
                    uCode = UNASSIGNED;
                }
            } else { // We've got trail surrogate.
                if (leadSurrogate !== -1) {
                    uCode = 0x10000 + (leadSurrogate - 0xD800) * 0x400 + (uCode - 0xDC00);
                    leadSurrogate = -1;
                } else {
                    // Incomplete surrogate pair - only trail surrogate found.
                    uCode = UNASSIGNED;
                }
                
            }
        }
        else if (leadSurrogate !== -1) {
            // Incomplete surrogate pair - only lead surrogate found.
            nextChar = uCode; uCode = UNASSIGNED; // Write an error, then current char.
            leadSurrogate = -1;
        }

        // 2. Convert uCode character.
        var dbcsCode = UNASSIGNED;
        if (seqObj !== undefined && uCode != UNASSIGNED) { // We are in the middle of the sequence
            var resCode = seqObj[uCode];
            if (typeof resCode === 'object') { // Sequence continues.
                seqObj = resCode;
                continue;

            } else if (typeof resCode == 'number') { // Sequence finished. Write it.
                dbcsCode = resCode;

            } else if (resCode == undefined) { // Current character is not part of the sequence.

                // Try default character for this sequence
                resCode = seqObj[DEF_CHAR];
                if (resCode !== undefined) {
                    dbcsCode = resCode; // Found. Write it.
                    nextChar = uCode; // Current character will be written too in the next iteration.

                } else {
                    // TODO: What if we have no default? (resCode == undefined)
                    // Then, we should write first char of the sequence as-is and try the rest recursively.
                    // Didn't do it for now because no encoding has this situation yet.
                    // Currently, just skip the sequence and write current char.
                }
            }
            seqObj = undefined;
        }
        else if (uCode >= 0) {  // Regular character
            var subtable = this.encodeTable[uCode >> 8];
            if (subtable !== undefined)
                dbcsCode = subtable[uCode & 0xFF];
            
            if (dbcsCode <= SEQ_START) { // Sequence start
                seqObj = this.encodeTableSeq[SEQ_START-dbcsCode];
                continue;
            }

            if (dbcsCode == UNASSIGNED && this.gb18030) {
                // Use GB18030 algorithm to find character(s) to write.
                var idx = findIdx(this.gb18030.uChars, uCode);
                if (idx != -1) {
                    var dbcsCode = this.gb18030.gbChars[idx] + (uCode - this.gb18030.uChars[idx]);
                    newBuf[j++] = 0x81 + Math.floor(dbcsCode / 12600); dbcsCode = dbcsCode % 12600;
                    newBuf[j++] = 0x30 + Math.floor(dbcsCode / 1260); dbcsCode = dbcsCode % 1260;
                    newBuf[j++] = 0x81 + Math.floor(dbcsCode / 10); dbcsCode = dbcsCode % 10;
                    newBuf[j++] = 0x30 + dbcsCode;
                    continue;
                }
            }
        }

        // 3. Write dbcsCode character.
        if (dbcsCode === UNASSIGNED)
            dbcsCode = this.defaultCharSingleByte;
        
        if (dbcsCode < 0x100) {
            newBuf[j++] = dbcsCode;
        }
        else if (dbcsCode < 0x10000) {
            newBuf[j++] = dbcsCode >> 8;   // high byte
            newBuf[j++] = dbcsCode & 0xFF; // low byte
        }
        else if (dbcsCode < 0x1000000) {
            newBuf[j++] = dbcsCode >> 16;
            newBuf[j++] = (dbcsCode >> 8) & 0xFF;
            newBuf[j++] = dbcsCode & 0xFF;
        } else {
            newBuf[j++] = dbcsCode >>> 24;
            newBuf[j++] = (dbcsCode >>> 16) & 0xFF;
            newBuf[j++] = (dbcsCode >>> 8) & 0xFF;
            newBuf[j++] = dbcsCode & 0xFF;
        }
    }

    this.seqObj = seqObj;
    this.leadSurrogate = leadSurrogate;
    return newBuf.slice(0, j);
}

DBCSEncoder.prototype.end = function() {
    if (this.leadSurrogate === -1 && this.seqObj === undefined)
        return; // All clean. Most often case.

    var newBuf = Buffer.alloc(10), j = 0;

    if (this.seqObj) { // We're in the sequence.
        var dbcsCode = this.seqObj[DEF_CHAR];
        if (dbcsCode !== undefined) { // Write beginning of the sequence.
            if (dbcsCode < 0x100) {
                newBuf[j++] = dbcsCode;
            }
            else {
                newBuf[j++] = dbcsCode >> 8;   // high byte
                newBuf[j++] = dbcsCode & 0xFF; // low byte
            }
        } else {
            // See todo above.
        }
        this.seqObj = undefined;
    }

    if (this.leadSurrogate !== -1) {
        // Incomplete surrogate pair - only lead surrogate found.
        newBuf[j++] = this.defaultCharSingleByte;
        this.leadSurrogate = -1;
    }
    
    return newBuf.slice(0, j);
}

// Export for testing
DBCSEncoder.prototype.findIdx = findIdx;


// == Decoder ==================================================================

function DBCSDecoder(options, codec) {
    // Decoder state
    this.nodeIdx = 0;
    this.prevBytes = [];

    // Static data
    this.decodeTables = codec.decodeTables;
    this.decodeTableSeq = codec.decodeTableSeq;
    this.defaultCharUnicode = codec.defaultCharUnicode;
    this.gb18030 = codec.gb18030;
}

DBCSDecoder.prototype.write = function(buf) {
    var newBuf = Buffer.alloc(buf.length*2),
        nodeIdx = this.nodeIdx, 
        prevBytes = this.prevBytes, prevOffset = this.prevBytes.length,
        seqStart = -this.prevBytes.length, // idx of the start of current parsed sequence.
        uCode;

    for (var i = 0, j = 0; i < buf.length; i++) {
        var curByte = (i >= 0) ? buf[i] : prevBytes[i + prevOffset];

        // Lookup in current trie node.
        var uCode = this.decodeTables[nodeIdx][curByte];

        if (uCode >= 0) { 
            // Normal character, just use it.
        }
        else if (uCode === UNASSIGNED) { // Unknown char.
            // TODO: Callback with seq.
            uCode = this.defaultCharUnicode.charCodeAt(0);
            i = seqStart; // Skip one byte ('i' will be incremented by the for loop) and try to parse again.
        }
        else if (uCode === GB18030_CODE) {
            if (i >= 3) {
                var ptr = (buf[i-3]-0x81)*12600 + (buf[i-2]-0x30)*1260 + (buf[i-1]-0x81)*10 + (curByte-0x30);
            } else {
                var ptr = (prevBytes[i-3+prevOffset]-0x81)*12600 + 
                          (((i-2 >= 0) ? buf[i-2] : prevBytes[i-2+prevOffset])-0x30)*1260 + 
                          (((i-1 >= 0) ? buf[i-1] : prevBytes[i-1+prevOffset])-0x81)*10 + 
                          (curByte-0x30);
            }
            var idx = findIdx(this.gb18030.gbChars, ptr);
            uCode = this.gb18030.uChars[idx] + ptr - this.gb18030.gbChars[idx];
        }
        else if (uCode <= NODE_START) { // Go to next trie node.
            nodeIdx = NODE_START - uCode;
            continue;
        }
        else if (uCode <= SEQ_START) { // Output a sequence of chars.
            var seq = this.decodeTableSeq[SEQ_START - uCode];
            for (var k = 0; k < seq.length - 1; k++) {
                uCode = seq[k];
                newBuf[j++] = uCode & 0xFF;
                newBuf[j++] = uCode >> 8;
            }
            uCode = seq[seq.length-1];
        }
        else
            throw new Error("iconv-lite internal error: invalid decoding table value " + uCode + " at " + nodeIdx + "/" + curByte);

        // Write the character to buffer, handling higher planes using surrogate pair.
        if (uCode >= 0x10000) { 
            uCode -= 0x10000;
            var uCodeLead = 0xD800 | (uCode >> 10);
            newBuf[j++] = uCodeLead & 0xFF;
            newBuf[j++] = uCodeLead >> 8;

            uCode = 0xDC00 | (uCode & 0x3FF);
        }
        newBuf[j++] = uCode & 0xFF;
        newBuf[j++] = uCode >> 8;

        // Reset trie node.
        nodeIdx = 0; seqStart = i+1;
    }

    this.nodeIdx = nodeIdx;
    this.prevBytes = (seqStart >= 0)
        ? Array.prototype.slice.call(buf, seqStart)
        : prevBytes.slice(seqStart + prevOffset).concat(Array.prototype.slice.call(buf));

    return newBuf.slice(0, j).toString('ucs2');
}

DBCSDecoder.prototype.end = function() {
    var ret = '';

    // Try to parse all remaining chars.
    while (this.prevBytes.length > 0) {
        // Skip 1 character in the buffer.
        ret += this.defaultCharUnicode;
        var bytesArr = this.prevBytes.slice(1);

        // Parse remaining as usual.
        this.prevBytes = [];
        this.nodeIdx = 0;
        if (bytesArr.length > 0)
            ret += this.write(bytesArr);
    }

    this.prevBytes = [];
    this.nodeIdx = 0;
    return ret;
}

// Binary search for GB18030. Returns largest i such that table[i] <= val.
function findIdx(table, val) {
    if (table[0] > val)
        return -1;

    var l = 0, r = table.length;
    while (l < r-1) { // always table[l] <= val < table[r]
        var mid = l + ((r-l+1) >> 1);
        if (table[mid] <= val)
            l = mid;
        else
            r = mid;
    }
    return l;
}



/***/ }),
/* 399 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// Description of supported double byte encodings and aliases.
// Tables are not require()-d until they are needed to speed up library load.
// require()-s are direct to support Browserify.

module.exports = {
    
    // == Japanese/ShiftJIS ====================================================
    // All japanese encodings are based on JIS X set of standards:
    // JIS X 0201 - Single-byte encoding of ASCII + ¥ + Kana chars at 0xA1-0xDF.
    // JIS X 0208 - Main set of 6879 characters, placed in 94x94 plane, to be encoded by 2 bytes. 
    //              Has several variations in 1978, 1983, 1990 and 1997.
    // JIS X 0212 - Supplementary plane of 6067 chars in 94x94 plane. 1990. Effectively dead.
    // JIS X 0213 - Extension and modern replacement of 0208 and 0212. Total chars: 11233.
    //              2 planes, first is superset of 0208, second - revised 0212.
    //              Introduced in 2000, revised 2004. Some characters are in Unicode Plane 2 (0x2xxxx)

    // Byte encodings are:
    //  * Shift_JIS: Compatible with 0201, uses not defined chars in top half as lead bytes for double-byte
    //               encoding of 0208. Lead byte ranges: 0x81-0x9F, 0xE0-0xEF; Trail byte ranges: 0x40-0x7E, 0x80-0x9E, 0x9F-0xFC.
    //               Windows CP932 is a superset of Shift_JIS. Some companies added more chars, notably KDDI.
    //  * EUC-JP:    Up to 3 bytes per character. Used mostly on *nixes.
    //               0x00-0x7F       - lower part of 0201
    //               0x8E, 0xA1-0xDF - upper part of 0201
    //               (0xA1-0xFE)x2   - 0208 plane (94x94).
    //               0x8F, (0xA1-0xFE)x2 - 0212 plane (94x94).
    //  * JIS X 208: 7-bit, direct encoding of 0208. Byte ranges: 0x21-0x7E (94 values). Uncommon.
    //               Used as-is in ISO2022 family.
    //  * ISO2022-JP: Stateful encoding, with escape sequences to switch between ASCII, 
    //                0201-1976 Roman, 0208-1978, 0208-1983.
    //  * ISO2022-JP-1: Adds esc seq for 0212-1990.
    //  * ISO2022-JP-2: Adds esc seq for GB2313-1980, KSX1001-1992, ISO8859-1, ISO8859-7.
    //  * ISO2022-JP-3: Adds esc seq for 0201-1976 Kana set, 0213-2000 Planes 1, 2.
    //  * ISO2022-JP-2004: Adds 0213-2004 Plane 1.
    //
    // After JIS X 0213 appeared, Shift_JIS-2004, EUC-JISX0213 and ISO2022-JP-2004 followed, with just changing the planes.
    //
    // Overall, it seems that it's a mess :( http://www8.plala.or.jp/tkubota1/unicode-symbols-map2.html

    'shiftjis': {
        type: '_dbcs',
        table: function() { return __webpack_require__(400) },
        encodeAdd: {'\u00a5': 0x5C, '\u203E': 0x7E},
        encodeSkipVals: [{from: 0xED40, to: 0xF940}],
    },
    'csshiftjis': 'shiftjis',
    'mskanji': 'shiftjis',
    'sjis': 'shiftjis',
    'windows31j': 'shiftjis',
    'ms31j': 'shiftjis',
    'xsjis': 'shiftjis',
    'windows932': 'shiftjis',
    'ms932': 'shiftjis',
    '932': 'shiftjis',
    'cp932': 'shiftjis',

    'eucjp': {
        type: '_dbcs',
        table: function() { return __webpack_require__(401) },
        encodeAdd: {'\u00a5': 0x5C, '\u203E': 0x7E},
    },

    // TODO: KDDI extension to Shift_JIS
    // TODO: IBM CCSID 942 = CP932, but F0-F9 custom chars and other char changes.
    // TODO: IBM CCSID 943 = Shift_JIS = CP932 with original Shift_JIS lower 128 chars.


    // == Chinese/GBK ==========================================================
    // http://en.wikipedia.org/wiki/GBK
    // We mostly implement W3C recommendation: https://www.w3.org/TR/encoding/#gbk-encoder

    // Oldest GB2312 (1981, ~7600 chars) is a subset of CP936
    'gb2312': 'cp936',
    'gb231280': 'cp936',
    'gb23121980': 'cp936',
    'csgb2312': 'cp936',
    'csiso58gb231280': 'cp936',
    'euccn': 'cp936',

    // Microsoft's CP936 is a subset and approximation of GBK.
    'windows936': 'cp936',
    'ms936': 'cp936',
    '936': 'cp936',
    'cp936': {
        type: '_dbcs',
        table: function() { return __webpack_require__(171) },
    },

    // GBK (~22000 chars) is an extension of CP936 that added user-mapped chars and some other.
    'gbk': {
        type: '_dbcs',
        table: function() { return __webpack_require__(171).concat(__webpack_require__(250)) },
    },
    'xgbk': 'gbk',
    'isoir58': 'gbk',

    // GB18030 is an algorithmic extension of GBK.
    // Main source: https://www.w3.org/TR/encoding/#gbk-encoder
    // http://icu-project.org/docs/papers/gb18030.html
    // http://source.icu-project.org/repos/icu/data/trunk/charset/data/xml/gb-18030-2000.xml
    // http://www.khngai.com/chinese/charmap/tblgbk.php?page=0
    'gb18030': {
        type: '_dbcs',
        table: function() { return __webpack_require__(171).concat(__webpack_require__(250)) },
        gb18030: function() { return __webpack_require__(402) },
        encodeSkipVals: [0x80],
        encodeAdd: {'€': 0xA2E3},
    },

    'chinese': 'gb18030',


    // == Korean ===============================================================
    // EUC-KR, KS_C_5601 and KS X 1001 are exactly the same.
    'windows949': 'cp949',
    'ms949': 'cp949',
    '949': 'cp949',
    'cp949': {
        type: '_dbcs',
        table: function() { return __webpack_require__(403) },
    },

    'cseuckr': 'cp949',
    'csksc56011987': 'cp949',
    'euckr': 'cp949',
    'isoir149': 'cp949',
    'korean': 'cp949',
    'ksc56011987': 'cp949',
    'ksc56011989': 'cp949',
    'ksc5601': 'cp949',


    // == Big5/Taiwan/Hong Kong ================================================
    // There are lots of tables for Big5 and cp950. Please see the following links for history:
    // http://moztw.org/docs/big5/  http://www.haible.de/bruno/charsets/conversion-tables/Big5.html
    // Variations, in roughly number of defined chars:
    //  * Windows CP 950: Microsoft variant of Big5. Canonical: http://www.unicode.org/Public/MAPPINGS/VENDORS/MICSFT/WINDOWS/CP950.TXT
    //  * Windows CP 951: Microsoft variant of Big5-HKSCS-2001. Seems to be never public. http://me.abelcheung.org/articles/research/what-is-cp951/
    //  * Big5-2003 (Taiwan standard) almost superset of cp950.
    //  * Unicode-at-on (UAO) / Mozilla 1.8. Falling out of use on the Web. Not supported by other browsers.
    //  * Big5-HKSCS (-2001, -2004, -2008). Hong Kong standard. 
    //    many unicode code points moved from PUA to Supplementary plane (U+2XXXX) over the years.
    //    Plus, it has 4 combining sequences.
    //    Seems that Mozilla refused to support it for 10 yrs. https://bugzilla.mozilla.org/show_bug.cgi?id=162431 https://bugzilla.mozilla.org/show_bug.cgi?id=310299
    //    because big5-hkscs is the only encoding to include astral characters in non-algorithmic way.
    //    Implementations are not consistent within browsers; sometimes labeled as just big5.
    //    MS Internet Explorer switches from big5 to big5-hkscs when a patch applied.
    //    Great discussion & recap of what's going on https://bugzilla.mozilla.org/show_bug.cgi?id=912470#c31
    //    In the encoder, it might make sense to support encoding old PUA mappings to Big5 bytes seq-s.
    //    Official spec: http://www.ogcio.gov.hk/en/business/tech_promotion/ccli/terms/doc/2003cmp_2008.txt
    //                   http://www.ogcio.gov.hk/tc/business/tech_promotion/ccli/terms/doc/hkscs-2008-big5-iso.txt
    // 
    // Current understanding of how to deal with Big5(-HKSCS) is in the Encoding Standard, http://encoding.spec.whatwg.org/#big5-encoder
    // Unicode mapping (http://www.unicode.org/Public/MAPPINGS/OBSOLETE/EASTASIA/OTHER/BIG5.TXT) is said to be wrong.

    'windows950': 'cp950',
    'ms950': 'cp950',
    '950': 'cp950',
    'cp950': {
        type: '_dbcs',
        table: function() { return __webpack_require__(251) },
    },

    // Big5 has many variations and is an extension of cp950. We use Encoding Standard's as a consensus.
    'big5': 'big5hkscs',
    'big5hkscs': {
        type: '_dbcs',
        table: function() { return __webpack_require__(251).concat(__webpack_require__(404)) },
        encodeSkipVals: [0xa2cc],
    },

    'cnbig5': 'big5hkscs',
    'csbig5': 'big5hkscs',
    'xxbig5': 'big5hkscs',
};


/***/ }),
/* 400 */
/***/ (function(module) {

module.exports = JSON.parse("[[\"0\",\"\\u0000\",128],[\"a1\",\"｡\",62],[\"8140\",\"　、。，．・：；？！゛゜´｀¨＾￣＿ヽヾゝゞ〃仝々〆〇ー―‐／＼～∥｜…‥‘’“”（）〔〕［］｛｝〈\",9,\"＋－±×\"],[\"8180\",\"÷＝≠＜＞≦≧∞∴♂♀°′″℃￥＄￠￡％＃＆＊＠§☆★○●◎◇◆□■△▲▽▼※〒→←↑↓〓\"],[\"81b8\",\"∈∋⊆⊇⊂⊃∪∩\"],[\"81c8\",\"∧∨￢⇒⇔∀∃\"],[\"81da\",\"∠⊥⌒∂∇≡≒≪≫√∽∝∵∫∬\"],[\"81f0\",\"Å‰♯♭♪†‡¶\"],[\"81fc\",\"◯\"],[\"824f\",\"０\",9],[\"8260\",\"Ａ\",25],[\"8281\",\"ａ\",25],[\"829f\",\"ぁ\",82],[\"8340\",\"ァ\",62],[\"8380\",\"ム\",22],[\"839f\",\"Α\",16,\"Σ\",6],[\"83bf\",\"α\",16,\"σ\",6],[\"8440\",\"А\",5,\"ЁЖ\",25],[\"8470\",\"а\",5,\"ёж\",7],[\"8480\",\"о\",17],[\"849f\",\"─│┌┐┘└├┬┤┴┼━┃┏┓┛┗┣┳┫┻╋┠┯┨┷┿┝┰┥┸╂\"],[\"8740\",\"①\",19,\"Ⅰ\",9],[\"875f\",\"㍉㌔㌢㍍㌘㌧㌃㌶㍑㍗㌍㌦㌣㌫㍊㌻㎜㎝㎞㎎㎏㏄㎡\"],[\"877e\",\"㍻\"],[\"8780\",\"〝〟№㏍℡㊤\",4,\"㈱㈲㈹㍾㍽㍼≒≡∫∮∑√⊥∠∟⊿∵∩∪\"],[\"889f\",\"亜唖娃阿哀愛挨姶逢葵茜穐悪握渥旭葦芦鯵梓圧斡扱宛姐虻飴絢綾鮎或粟袷安庵按暗案闇鞍杏以伊位依偉囲夷委威尉惟意慰易椅為畏異移維緯胃萎衣謂違遺医井亥域育郁磯一壱溢逸稲茨芋鰯允印咽員因姻引飲淫胤蔭\"],[\"8940\",\"院陰隠韻吋右宇烏羽迂雨卯鵜窺丑碓臼渦嘘唄欝蔚鰻姥厩浦瓜閏噂云運雲荏餌叡営嬰影映曳栄永泳洩瑛盈穎頴英衛詠鋭液疫益駅悦謁越閲榎厭円\"],[\"8980\",\"園堰奄宴延怨掩援沿演炎焔煙燕猿縁艶苑薗遠鉛鴛塩於汚甥凹央奥往応押旺横欧殴王翁襖鴬鴎黄岡沖荻億屋憶臆桶牡乙俺卸恩温穏音下化仮何伽価佳加可嘉夏嫁家寡科暇果架歌河火珂禍禾稼箇花苛茄荷華菓蝦課嘩貨迦過霞蚊俄峨我牙画臥芽蛾賀雅餓駕介会解回塊壊廻快怪悔恢懐戒拐改\"],[\"8a40\",\"魁晦械海灰界皆絵芥蟹開階貝凱劾外咳害崖慨概涯碍蓋街該鎧骸浬馨蛙垣柿蛎鈎劃嚇各廓拡撹格核殻獲確穫覚角赫較郭閣隔革学岳楽額顎掛笠樫\"],[\"8a80\",\"橿梶鰍潟割喝恰括活渇滑葛褐轄且鰹叶椛樺鞄株兜竃蒲釜鎌噛鴨栢茅萱粥刈苅瓦乾侃冠寒刊勘勧巻喚堪姦完官寛干幹患感慣憾換敢柑桓棺款歓汗漢澗潅環甘監看竿管簡緩缶翰肝艦莞観諌貫還鑑間閑関陥韓館舘丸含岸巌玩癌眼岩翫贋雁頑顔願企伎危喜器基奇嬉寄岐希幾忌揮机旗既期棋棄\"],[\"8b40\",\"機帰毅気汽畿祈季稀紀徽規記貴起軌輝飢騎鬼亀偽儀妓宜戯技擬欺犠疑祇義蟻誼議掬菊鞠吉吃喫桔橘詰砧杵黍却客脚虐逆丘久仇休及吸宮弓急救\"],[\"8b80\",\"朽求汲泣灸球究窮笈級糾給旧牛去居巨拒拠挙渠虚許距鋸漁禦魚亨享京供侠僑兇競共凶協匡卿叫喬境峡強彊怯恐恭挟教橋況狂狭矯胸脅興蕎郷鏡響饗驚仰凝尭暁業局曲極玉桐粁僅勤均巾錦斤欣欽琴禁禽筋緊芹菌衿襟謹近金吟銀九倶句区狗玖矩苦躯駆駈駒具愚虞喰空偶寓遇隅串櫛釧屑屈\"],[\"8c40\",\"掘窟沓靴轡窪熊隈粂栗繰桑鍬勲君薫訓群軍郡卦袈祁係傾刑兄啓圭珪型契形径恵慶慧憩掲携敬景桂渓畦稽系経継繋罫茎荊蛍計詣警軽頚鶏芸迎鯨\"],[\"8c80\",\"劇戟撃激隙桁傑欠決潔穴結血訣月件倹倦健兼券剣喧圏堅嫌建憲懸拳捲検権牽犬献研硯絹県肩見謙賢軒遣鍵険顕験鹸元原厳幻弦減源玄現絃舷言諺限乎個古呼固姑孤己庫弧戸故枯湖狐糊袴股胡菰虎誇跨鈷雇顧鼓五互伍午呉吾娯後御悟梧檎瑚碁語誤護醐乞鯉交佼侯候倖光公功効勾厚口向\"],[\"8d40\",\"后喉坑垢好孔孝宏工巧巷幸広庚康弘恒慌抗拘控攻昂晃更杭校梗構江洪浩港溝甲皇硬稿糠紅紘絞綱耕考肯肱腔膏航荒行衡講貢購郊酵鉱砿鋼閤降\"],[\"8d80\",\"項香高鴻剛劫号合壕拷濠豪轟麹克刻告国穀酷鵠黒獄漉腰甑忽惚骨狛込此頃今困坤墾婚恨懇昏昆根梱混痕紺艮魂些佐叉唆嵯左差査沙瑳砂詐鎖裟坐座挫債催再最哉塞妻宰彩才採栽歳済災采犀砕砦祭斎細菜裁載際剤在材罪財冴坂阪堺榊肴咲崎埼碕鷺作削咋搾昨朔柵窄策索錯桜鮭笹匙冊刷\"],[\"8e40\",\"察拶撮擦札殺薩雑皐鯖捌錆鮫皿晒三傘参山惨撒散桟燦珊産算纂蚕讃賛酸餐斬暫残仕仔伺使刺司史嗣四士始姉姿子屍市師志思指支孜斯施旨枝止\"],[\"8e80\",\"死氏獅祉私糸紙紫肢脂至視詞詩試誌諮資賜雌飼歯事似侍児字寺慈持時次滋治爾璽痔磁示而耳自蒔辞汐鹿式識鴫竺軸宍雫七叱執失嫉室悉湿漆疾質実蔀篠偲柴芝屡蕊縞舎写射捨赦斜煮社紗者謝車遮蛇邪借勺尺杓灼爵酌釈錫若寂弱惹主取守手朱殊狩珠種腫趣酒首儒受呪寿授樹綬需囚収周\"],[\"8f40\",\"宗就州修愁拾洲秀秋終繍習臭舟蒐衆襲讐蹴輯週酋酬集醜什住充十従戎柔汁渋獣縦重銃叔夙宿淑祝縮粛塾熟出術述俊峻春瞬竣舜駿准循旬楯殉淳\"],[\"8f80\",\"準潤盾純巡遵醇順処初所暑曙渚庶緒署書薯藷諸助叙女序徐恕鋤除傷償勝匠升召哨商唱嘗奨妾娼宵将小少尚庄床廠彰承抄招掌捷昇昌昭晶松梢樟樵沼消渉湘焼焦照症省硝礁祥称章笑粧紹肖菖蒋蕉衝裳訟証詔詳象賞醤鉦鍾鐘障鞘上丈丞乗冗剰城場壌嬢常情擾条杖浄状畳穣蒸譲醸錠嘱埴飾\"],[\"9040\",\"拭植殖燭織職色触食蝕辱尻伸信侵唇娠寝審心慎振新晋森榛浸深申疹真神秦紳臣芯薪親診身辛進針震人仁刃塵壬尋甚尽腎訊迅陣靭笥諏須酢図厨\"],[\"9080\",\"逗吹垂帥推水炊睡粋翠衰遂酔錐錘随瑞髄崇嵩数枢趨雛据杉椙菅頗雀裾澄摺寸世瀬畝是凄制勢姓征性成政整星晴棲栖正清牲生盛精聖声製西誠誓請逝醒青静斉税脆隻席惜戚斥昔析石積籍績脊責赤跡蹟碩切拙接摂折設窃節説雪絶舌蝉仙先千占宣専尖川戦扇撰栓栴泉浅洗染潜煎煽旋穿箭線\"],[\"9140\",\"繊羨腺舛船薦詮賎践選遷銭銑閃鮮前善漸然全禅繕膳糎噌塑岨措曾曽楚狙疏疎礎祖租粗素組蘇訴阻遡鼠僧創双叢倉喪壮奏爽宋層匝惣想捜掃挿掻\"],[\"9180\",\"操早曹巣槍槽漕燥争痩相窓糟総綜聡草荘葬蒼藻装走送遭鎗霜騒像増憎臓蔵贈造促側則即息捉束測足速俗属賊族続卒袖其揃存孫尊損村遜他多太汰詑唾堕妥惰打柁舵楕陀駄騨体堆対耐岱帯待怠態戴替泰滞胎腿苔袋貸退逮隊黛鯛代台大第醍題鷹滝瀧卓啄宅托択拓沢濯琢託鐸濁諾茸凧蛸只\"],[\"9240\",\"叩但達辰奪脱巽竪辿棚谷狸鱈樽誰丹単嘆坦担探旦歎淡湛炭短端箪綻耽胆蛋誕鍛団壇弾断暖檀段男談値知地弛恥智池痴稚置致蜘遅馳築畜竹筑蓄\"],[\"9280\",\"逐秩窒茶嫡着中仲宙忠抽昼柱注虫衷註酎鋳駐樗瀦猪苧著貯丁兆凋喋寵帖帳庁弔張彫徴懲挑暢朝潮牒町眺聴脹腸蝶調諜超跳銚長頂鳥勅捗直朕沈珍賃鎮陳津墜椎槌追鎚痛通塚栂掴槻佃漬柘辻蔦綴鍔椿潰坪壷嬬紬爪吊釣鶴亭低停偵剃貞呈堤定帝底庭廷弟悌抵挺提梯汀碇禎程締艇訂諦蹄逓\"],[\"9340\",\"邸鄭釘鼎泥摘擢敵滴的笛適鏑溺哲徹撤轍迭鉄典填天展店添纏甜貼転顛点伝殿澱田電兎吐堵塗妬屠徒斗杜渡登菟賭途都鍍砥砺努度土奴怒倒党冬\"],[\"9380\",\"凍刀唐塔塘套宕島嶋悼投搭東桃梼棟盗淘湯涛灯燈当痘祷等答筒糖統到董蕩藤討謄豆踏逃透鐙陶頭騰闘働動同堂導憧撞洞瞳童胴萄道銅峠鴇匿得徳涜特督禿篤毒独読栃橡凸突椴届鳶苫寅酉瀞噸屯惇敦沌豚遁頓呑曇鈍奈那内乍凪薙謎灘捺鍋楢馴縄畷南楠軟難汝二尼弐迩匂賑肉虹廿日乳入\"],[\"9440\",\"如尿韮任妊忍認濡禰祢寧葱猫熱年念捻撚燃粘乃廼之埜嚢悩濃納能脳膿農覗蚤巴把播覇杷波派琶破婆罵芭馬俳廃拝排敗杯盃牌背肺輩配倍培媒梅\"],[\"9480\",\"楳煤狽買売賠陪這蝿秤矧萩伯剥博拍柏泊白箔粕舶薄迫曝漠爆縛莫駁麦函箱硲箸肇筈櫨幡肌畑畠八鉢溌発醗髪伐罰抜筏閥鳩噺塙蛤隼伴判半反叛帆搬斑板氾汎版犯班畔繁般藩販範釆煩頒飯挽晩番盤磐蕃蛮匪卑否妃庇彼悲扉批披斐比泌疲皮碑秘緋罷肥被誹費避非飛樋簸備尾微枇毘琵眉美\"],[\"9540\",\"鼻柊稗匹疋髭彦膝菱肘弼必畢筆逼桧姫媛紐百謬俵彪標氷漂瓢票表評豹廟描病秒苗錨鋲蒜蛭鰭品彬斌浜瀕貧賓頻敏瓶不付埠夫婦富冨布府怖扶敷\"],[\"9580\",\"斧普浮父符腐膚芙譜負賦赴阜附侮撫武舞葡蕪部封楓風葺蕗伏副復幅服福腹複覆淵弗払沸仏物鮒分吻噴墳憤扮焚奮粉糞紛雰文聞丙併兵塀幣平弊柄並蔽閉陛米頁僻壁癖碧別瞥蔑箆偏変片篇編辺返遍便勉娩弁鞭保舗鋪圃捕歩甫補輔穂募墓慕戊暮母簿菩倣俸包呆報奉宝峰峯崩庖抱捧放方朋\"],[\"9640\",\"法泡烹砲縫胞芳萌蓬蜂褒訪豊邦鋒飽鳳鵬乏亡傍剖坊妨帽忘忙房暴望某棒冒紡肪膨謀貌貿鉾防吠頬北僕卜墨撲朴牧睦穆釦勃没殆堀幌奔本翻凡盆\"],[\"9680\",\"摩磨魔麻埋妹昧枚毎哩槙幕膜枕鮪柾鱒桝亦俣又抹末沫迄侭繭麿万慢満漫蔓味未魅巳箕岬密蜜湊蓑稔脈妙粍民眠務夢無牟矛霧鵡椋婿娘冥名命明盟迷銘鳴姪牝滅免棉綿緬面麺摸模茂妄孟毛猛盲網耗蒙儲木黙目杢勿餅尤戻籾貰問悶紋門匁也冶夜爺耶野弥矢厄役約薬訳躍靖柳薮鑓愉愈油癒\"],[\"9740\",\"諭輸唯佑優勇友宥幽悠憂揖有柚湧涌猶猷由祐裕誘遊邑郵雄融夕予余与誉輿預傭幼妖容庸揚揺擁曜楊様洋溶熔用窯羊耀葉蓉要謡踊遥陽養慾抑欲\"],[\"9780\",\"沃浴翌翼淀羅螺裸来莱頼雷洛絡落酪乱卵嵐欄濫藍蘭覧利吏履李梨理璃痢裏裡里離陸律率立葎掠略劉流溜琉留硫粒隆竜龍侶慮旅虜了亮僚両凌寮料梁涼猟療瞭稜糧良諒遼量陵領力緑倫厘林淋燐琳臨輪隣鱗麟瑠塁涙累類令伶例冷励嶺怜玲礼苓鈴隷零霊麗齢暦歴列劣烈裂廉恋憐漣煉簾練聯\"],[\"9840\",\"蓮連錬呂魯櫓炉賂路露労婁廊弄朗楼榔浪漏牢狼篭老聾蝋郎六麓禄肋録論倭和話歪賄脇惑枠鷲亙亘鰐詫藁蕨椀湾碗腕\"],[\"989f\",\"弌丐丕个丱丶丼丿乂乖乘亂亅豫亊舒弍于亞亟亠亢亰亳亶从仍仄仆仂仗仞仭仟价伉佚估佛佝佗佇佶侈侏侘佻佩佰侑佯來侖儘俔俟俎俘俛俑俚俐俤俥倚倨倔倪倥倅伜俶倡倩倬俾俯們倆偃假會偕偐偈做偖偬偸傀傚傅傴傲\"],[\"9940\",\"僉僊傳僂僖僞僥僭僣僮價僵儉儁儂儖儕儔儚儡儺儷儼儻儿兀兒兌兔兢竸兩兪兮冀冂囘册冉冏冑冓冕冖冤冦冢冩冪冫决冱冲冰况冽凅凉凛几處凩凭\"],[\"9980\",\"凰凵凾刄刋刔刎刧刪刮刳刹剏剄剋剌剞剔剪剴剩剳剿剽劍劔劒剱劈劑辨辧劬劭劼劵勁勍勗勞勣勦飭勠勳勵勸勹匆匈甸匍匐匏匕匚匣匯匱匳匸區卆卅丗卉卍凖卞卩卮夘卻卷厂厖厠厦厥厮厰厶參簒雙叟曼燮叮叨叭叺吁吽呀听吭吼吮吶吩吝呎咏呵咎呟呱呷呰咒呻咀呶咄咐咆哇咢咸咥咬哄哈咨\"],[\"9a40\",\"咫哂咤咾咼哘哥哦唏唔哽哮哭哺哢唹啀啣啌售啜啅啖啗唸唳啝喙喀咯喊喟啻啾喘喞單啼喃喩喇喨嗚嗅嗟嗄嗜嗤嗔嘔嗷嘖嗾嗽嘛嗹噎噐營嘴嘶嘲嘸\"],[\"9a80\",\"噫噤嘯噬噪嚆嚀嚊嚠嚔嚏嚥嚮嚶嚴囂嚼囁囃囀囈囎囑囓囗囮囹圀囿圄圉圈國圍圓團圖嗇圜圦圷圸坎圻址坏坩埀垈坡坿垉垓垠垳垤垪垰埃埆埔埒埓堊埖埣堋堙堝塲堡塢塋塰毀塒堽塹墅墹墟墫墺壞墻墸墮壅壓壑壗壙壘壥壜壤壟壯壺壹壻壼壽夂夊夐夛梦夥夬夭夲夸夾竒奕奐奎奚奘奢奠奧奬奩\"],[\"9b40\",\"奸妁妝佞侫妣妲姆姨姜妍姙姚娥娟娑娜娉娚婀婬婉娵娶婢婪媚媼媾嫋嫂媽嫣嫗嫦嫩嫖嫺嫻嬌嬋嬖嬲嫐嬪嬶嬾孃孅孀孑孕孚孛孥孩孰孳孵學斈孺宀\"],[\"9b80\",\"它宦宸寃寇寉寔寐寤實寢寞寥寫寰寶寳尅將專對尓尠尢尨尸尹屁屆屎屓屐屏孱屬屮乢屶屹岌岑岔妛岫岻岶岼岷峅岾峇峙峩峽峺峭嶌峪崋崕崗嵜崟崛崑崔崢崚崙崘嵌嵒嵎嵋嵬嵳嵶嶇嶄嶂嶢嶝嶬嶮嶽嶐嶷嶼巉巍巓巒巖巛巫已巵帋帚帙帑帛帶帷幄幃幀幎幗幔幟幢幤幇幵并幺麼广庠廁廂廈廐廏\"],[\"9c40\",\"廖廣廝廚廛廢廡廨廩廬廱廳廰廴廸廾弃弉彝彜弋弑弖弩弭弸彁彈彌彎弯彑彖彗彙彡彭彳彷徃徂彿徊很徑徇從徙徘徠徨徭徼忖忻忤忸忱忝悳忿怡恠\"],[\"9c80\",\"怙怐怩怎怱怛怕怫怦怏怺恚恁恪恷恟恊恆恍恣恃恤恂恬恫恙悁悍惧悃悚悄悛悖悗悒悧悋惡悸惠惓悴忰悽惆悵惘慍愕愆惶惷愀惴惺愃愡惻惱愍愎慇愾愨愧慊愿愼愬愴愽慂慄慳慷慘慙慚慫慴慯慥慱慟慝慓慵憙憖憇憬憔憚憊憑憫憮懌懊應懷懈懃懆憺懋罹懍懦懣懶懺懴懿懽懼懾戀戈戉戍戌戔戛\"],[\"9d40\",\"戞戡截戮戰戲戳扁扎扞扣扛扠扨扼抂抉找抒抓抖拔抃抔拗拑抻拏拿拆擔拈拜拌拊拂拇抛拉挌拮拱挧挂挈拯拵捐挾捍搜捏掖掎掀掫捶掣掏掉掟掵捫\"],[\"9d80\",\"捩掾揩揀揆揣揉插揶揄搖搴搆搓搦搶攝搗搨搏摧摯摶摎攪撕撓撥撩撈撼據擒擅擇撻擘擂擱擧舉擠擡抬擣擯攬擶擴擲擺攀擽攘攜攅攤攣攫攴攵攷收攸畋效敖敕敍敘敞敝敲數斂斃變斛斟斫斷旃旆旁旄旌旒旛旙无旡旱杲昊昃旻杳昵昶昴昜晏晄晉晁晞晝晤晧晨晟晢晰暃暈暎暉暄暘暝曁暹曉暾暼\"],[\"9e40\",\"曄暸曖曚曠昿曦曩曰曵曷朏朖朞朦朧霸朮朿朶杁朸朷杆杞杠杙杣杤枉杰枩杼杪枌枋枦枡枅枷柯枴柬枳柩枸柤柞柝柢柮枹柎柆柧檜栞框栩桀桍栲桎\"],[\"9e80\",\"梳栫桙档桷桿梟梏梭梔條梛梃檮梹桴梵梠梺椏梍桾椁棊椈棘椢椦棡椌棍棔棧棕椶椒椄棗棣椥棹棠棯椨椪椚椣椡棆楹楷楜楸楫楔楾楮椹楴椽楙椰楡楞楝榁楪榲榮槐榿槁槓榾槎寨槊槝榻槃榧樮榑榠榜榕榴槞槨樂樛槿權槹槲槧樅榱樞槭樔槫樊樒櫁樣樓橄樌橲樶橸橇橢橙橦橈樸樢檐檍檠檄檢檣\"],[\"9f40\",\"檗蘗檻櫃櫂檸檳檬櫞櫑櫟檪櫚櫪櫻欅蘖櫺欒欖鬱欟欸欷盜欹飮歇歃歉歐歙歔歛歟歡歸歹歿殀殄殃殍殘殕殞殤殪殫殯殲殱殳殷殼毆毋毓毟毬毫毳毯\"],[\"9f80\",\"麾氈氓气氛氤氣汞汕汢汪沂沍沚沁沛汾汨汳沒沐泄泱泓沽泗泅泝沮沱沾沺泛泯泙泪洟衍洶洫洽洸洙洵洳洒洌浣涓浤浚浹浙涎涕濤涅淹渕渊涵淇淦涸淆淬淞淌淨淒淅淺淙淤淕淪淮渭湮渮渙湲湟渾渣湫渫湶湍渟湃渺湎渤滿渝游溂溪溘滉溷滓溽溯滄溲滔滕溏溥滂溟潁漑灌滬滸滾漿滲漱滯漲滌\"],[\"e040\",\"漾漓滷澆潺潸澁澀潯潛濳潭澂潼潘澎澑濂潦澳澣澡澤澹濆澪濟濕濬濔濘濱濮濛瀉瀋濺瀑瀁瀏濾瀛瀚潴瀝瀘瀟瀰瀾瀲灑灣炙炒炯烱炬炸炳炮烟烋烝\"],[\"e080\",\"烙焉烽焜焙煥煕熈煦煢煌煖煬熏燻熄熕熨熬燗熹熾燒燉燔燎燠燬燧燵燼燹燿爍爐爛爨爭爬爰爲爻爼爿牀牆牋牘牴牾犂犁犇犒犖犢犧犹犲狃狆狄狎狒狢狠狡狹狷倏猗猊猜猖猝猴猯猩猥猾獎獏默獗獪獨獰獸獵獻獺珈玳珎玻珀珥珮珞璢琅瑯琥珸琲琺瑕琿瑟瑙瑁瑜瑩瑰瑣瑪瑶瑾璋璞璧瓊瓏瓔珱\"],[\"e140\",\"瓠瓣瓧瓩瓮瓲瓰瓱瓸瓷甄甃甅甌甎甍甕甓甞甦甬甼畄畍畊畉畛畆畚畩畤畧畫畭畸當疆疇畴疊疉疂疔疚疝疥疣痂疳痃疵疽疸疼疱痍痊痒痙痣痞痾痿\"],[\"e180\",\"痼瘁痰痺痲痳瘋瘍瘉瘟瘧瘠瘡瘢瘤瘴瘰瘻癇癈癆癜癘癡癢癨癩癪癧癬癰癲癶癸發皀皃皈皋皎皖皓皙皚皰皴皸皹皺盂盍盖盒盞盡盥盧盪蘯盻眈眇眄眩眤眞眥眦眛眷眸睇睚睨睫睛睥睿睾睹瞎瞋瞑瞠瞞瞰瞶瞹瞿瞼瞽瞻矇矍矗矚矜矣矮矼砌砒礦砠礪硅碎硴碆硼碚碌碣碵碪碯磑磆磋磔碾碼磅磊磬\"],[\"e240\",\"磧磚磽磴礇礒礑礙礬礫祀祠祗祟祚祕祓祺祿禊禝禧齋禪禮禳禹禺秉秕秧秬秡秣稈稍稘稙稠稟禀稱稻稾稷穃穗穉穡穢穩龝穰穹穽窈窗窕窘窖窩竈窰\"],[\"e280\",\"窶竅竄窿邃竇竊竍竏竕竓站竚竝竡竢竦竭竰笂笏笊笆笳笘笙笞笵笨笶筐筺笄筍笋筌筅筵筥筴筧筰筱筬筮箝箘箟箍箜箚箋箒箏筝箙篋篁篌篏箴篆篝篩簑簔篦篥籠簀簇簓篳篷簗簍篶簣簧簪簟簷簫簽籌籃籔籏籀籐籘籟籤籖籥籬籵粃粐粤粭粢粫粡粨粳粲粱粮粹粽糀糅糂糘糒糜糢鬻糯糲糴糶糺紆\"],[\"e340\",\"紂紜紕紊絅絋紮紲紿紵絆絳絖絎絲絨絮絏絣經綉絛綏絽綛綺綮綣綵緇綽綫總綢綯緜綸綟綰緘緝緤緞緻緲緡縅縊縣縡縒縱縟縉縋縢繆繦縻縵縹繃縷\"],[\"e380\",\"縲縺繧繝繖繞繙繚繹繪繩繼繻纃緕繽辮繿纈纉續纒纐纓纔纖纎纛纜缸缺罅罌罍罎罐网罕罔罘罟罠罨罩罧罸羂羆羃羈羇羌羔羞羝羚羣羯羲羹羮羶羸譱翅翆翊翕翔翡翦翩翳翹飜耆耄耋耒耘耙耜耡耨耿耻聊聆聒聘聚聟聢聨聳聲聰聶聹聽聿肄肆肅肛肓肚肭冐肬胛胥胙胝胄胚胖脉胯胱脛脩脣脯腋\"],[\"e440\",\"隋腆脾腓腑胼腱腮腥腦腴膃膈膊膀膂膠膕膤膣腟膓膩膰膵膾膸膽臀臂膺臉臍臑臙臘臈臚臟臠臧臺臻臾舁舂舅與舊舍舐舖舩舫舸舳艀艙艘艝艚艟艤\"],[\"e480\",\"艢艨艪艫舮艱艷艸艾芍芒芫芟芻芬苡苣苟苒苴苳苺莓范苻苹苞茆苜茉苙茵茴茖茲茱荀茹荐荅茯茫茗茘莅莚莪莟莢莖茣莎莇莊荼莵荳荵莠莉莨菴萓菫菎菽萃菘萋菁菷萇菠菲萍萢萠莽萸蔆菻葭萪萼蕚蒄葷葫蒭葮蒂葩葆萬葯葹萵蓊葢蒹蒿蒟蓙蓍蒻蓚蓐蓁蓆蓖蒡蔡蓿蓴蔗蔘蔬蔟蔕蔔蓼蕀蕣蕘蕈\"],[\"e540\",\"蕁蘂蕋蕕薀薤薈薑薊薨蕭薔薛藪薇薜蕷蕾薐藉薺藏薹藐藕藝藥藜藹蘊蘓蘋藾藺蘆蘢蘚蘰蘿虍乕虔號虧虱蚓蚣蚩蚪蚋蚌蚶蚯蛄蛆蚰蛉蠣蚫蛔蛞蛩蛬\"],[\"e580\",\"蛟蛛蛯蜒蜆蜈蜀蜃蛻蜑蜉蜍蛹蜊蜴蜿蜷蜻蜥蜩蜚蝠蝟蝸蝌蝎蝴蝗蝨蝮蝙蝓蝣蝪蠅螢螟螂螯蟋螽蟀蟐雖螫蟄螳蟇蟆螻蟯蟲蟠蠏蠍蟾蟶蟷蠎蟒蠑蠖蠕蠢蠡蠱蠶蠹蠧蠻衄衂衒衙衞衢衫袁衾袞衵衽袵衲袂袗袒袮袙袢袍袤袰袿袱裃裄裔裘裙裝裹褂裼裴裨裲褄褌褊褓襃褞褥褪褫襁襄褻褶褸襌褝襠襞\"],[\"e640\",\"襦襤襭襪襯襴襷襾覃覈覊覓覘覡覩覦覬覯覲覺覽覿觀觚觜觝觧觴觸訃訖訐訌訛訝訥訶詁詛詒詆詈詼詭詬詢誅誂誄誨誡誑誥誦誚誣諄諍諂諚諫諳諧\"],[\"e680\",\"諤諱謔諠諢諷諞諛謌謇謚諡謖謐謗謠謳鞫謦謫謾謨譁譌譏譎證譖譛譚譫譟譬譯譴譽讀讌讎讒讓讖讙讚谺豁谿豈豌豎豐豕豢豬豸豺貂貉貅貊貍貎貔豼貘戝貭貪貽貲貳貮貶賈賁賤賣賚賽賺賻贄贅贊贇贏贍贐齎贓賍贔贖赧赭赱赳趁趙跂趾趺跏跚跖跌跛跋跪跫跟跣跼踈踉跿踝踞踐踟蹂踵踰踴蹊\"],[\"e740\",\"蹇蹉蹌蹐蹈蹙蹤蹠踪蹣蹕蹶蹲蹼躁躇躅躄躋躊躓躑躔躙躪躡躬躰軆躱躾軅軈軋軛軣軼軻軫軾輊輅輕輒輙輓輜輟輛輌輦輳輻輹轅轂輾轌轉轆轎轗轜\"],[\"e780\",\"轢轣轤辜辟辣辭辯辷迚迥迢迪迯邇迴逅迹迺逑逕逡逍逞逖逋逧逶逵逹迸遏遐遑遒逎遉逾遖遘遞遨遯遶隨遲邂遽邁邀邊邉邏邨邯邱邵郢郤扈郛鄂鄒鄙鄲鄰酊酖酘酣酥酩酳酲醋醉醂醢醫醯醪醵醴醺釀釁釉釋釐釖釟釡釛釼釵釶鈞釿鈔鈬鈕鈑鉞鉗鉅鉉鉤鉈銕鈿鉋鉐銜銖銓銛鉚鋏銹銷鋩錏鋺鍄錮\"],[\"e840\",\"錙錢錚錣錺錵錻鍜鍠鍼鍮鍖鎰鎬鎭鎔鎹鏖鏗鏨鏥鏘鏃鏝鏐鏈鏤鐚鐔鐓鐃鐇鐐鐶鐫鐵鐡鐺鑁鑒鑄鑛鑠鑢鑞鑪鈩鑰鑵鑷鑽鑚鑼鑾钁鑿閂閇閊閔閖閘閙\"],[\"e880\",\"閠閨閧閭閼閻閹閾闊濶闃闍闌闕闔闖關闡闥闢阡阨阮阯陂陌陏陋陷陜陞陝陟陦陲陬隍隘隕隗險隧隱隲隰隴隶隸隹雎雋雉雍襍雜霍雕雹霄霆霈霓霎霑霏霖霙霤霪霰霹霽霾靄靆靈靂靉靜靠靤靦靨勒靫靱靹鞅靼鞁靺鞆鞋鞏鞐鞜鞨鞦鞣鞳鞴韃韆韈韋韜韭齏韲竟韶韵頏頌頸頤頡頷頽顆顏顋顫顯顰\"],[\"e940\",\"顱顴顳颪颯颱颶飄飃飆飩飫餃餉餒餔餘餡餝餞餤餠餬餮餽餾饂饉饅饐饋饑饒饌饕馗馘馥馭馮馼駟駛駝駘駑駭駮駱駲駻駸騁騏騅駢騙騫騷驅驂驀驃\"],[\"e980\",\"騾驕驍驛驗驟驢驥驤驩驫驪骭骰骼髀髏髑髓體髞髟髢髣髦髯髫髮髴髱髷髻鬆鬘鬚鬟鬢鬣鬥鬧鬨鬩鬪鬮鬯鬲魄魃魏魍魎魑魘魴鮓鮃鮑鮖鮗鮟鮠鮨鮴鯀鯊鮹鯆鯏鯑鯒鯣鯢鯤鯔鯡鰺鯲鯱鯰鰕鰔鰉鰓鰌鰆鰈鰒鰊鰄鰮鰛鰥鰤鰡鰰鱇鰲鱆鰾鱚鱠鱧鱶鱸鳧鳬鳰鴉鴈鳫鴃鴆鴪鴦鶯鴣鴟鵄鴕鴒鵁鴿鴾鵆鵈\"],[\"ea40\",\"鵝鵞鵤鵑鵐鵙鵲鶉鶇鶫鵯鵺鶚鶤鶩鶲鷄鷁鶻鶸鶺鷆鷏鷂鷙鷓鷸鷦鷭鷯鷽鸚鸛鸞鹵鹹鹽麁麈麋麌麒麕麑麝麥麩麸麪麭靡黌黎黏黐黔黜點黝黠黥黨黯\"],[\"ea80\",\"黴黶黷黹黻黼黽鼇鼈皷鼕鼡鼬鼾齊齒齔齣齟齠齡齦齧齬齪齷齲齶龕龜龠堯槇遙瑤凜熙\"],[\"ed40\",\"纊褜鍈銈蓜俉炻昱棈鋹曻彅丨仡仼伀伃伹佖侒侊侚侔俍偀倢俿倞偆偰偂傔僴僘兊兤冝冾凬刕劜劦勀勛匀匇匤卲厓厲叝﨎咜咊咩哿喆坙坥垬埈埇﨏\"],[\"ed80\",\"塚增墲夋奓奛奝奣妤妺孖寀甯寘寬尞岦岺峵崧嵓﨑嵂嵭嶸嶹巐弡弴彧德忞恝悅悊惞惕愠惲愑愷愰憘戓抦揵摠撝擎敎昀昕昻昉昮昞昤晥晗晙晴晳暙暠暲暿曺朎朗杦枻桒柀栁桄棏﨓楨﨔榘槢樰橫橆橳橾櫢櫤毖氿汜沆汯泚洄涇浯涖涬淏淸淲淼渹湜渧渼溿澈澵濵瀅瀇瀨炅炫焏焄煜煆煇凞燁燾犱\"],[\"ee40\",\"犾猤猪獷玽珉珖珣珒琇珵琦琪琩琮瑢璉璟甁畯皂皜皞皛皦益睆劯砡硎硤硺礰礼神祥禔福禛竑竧靖竫箞精絈絜綷綠緖繒罇羡羽茁荢荿菇菶葈蒴蕓蕙\"],[\"ee80\",\"蕫﨟薰蘒﨡蠇裵訒訷詹誧誾諟諸諶譓譿賰賴贒赶﨣軏﨤逸遧郞都鄕鄧釚釗釞釭釮釤釥鈆鈐鈊鈺鉀鈼鉎鉙鉑鈹鉧銧鉷鉸鋧鋗鋙鋐﨧鋕鋠鋓錥錡鋻﨨錞鋿錝錂鍰鍗鎤鏆鏞鏸鐱鑅鑈閒隆﨩隝隯霳霻靃靍靏靑靕顗顥飯飼餧館馞驎髙髜魵魲鮏鮱鮻鰀鵰鵫鶴鸙黑\"],[\"eeef\",\"ⅰ\",9,\"￢￤＇＂\"],[\"f040\",\"\",62],[\"f080\",\"\",124],[\"f140\",\"\",62],[\"f180\",\"\",124],[\"f240\",\"\",62],[\"f280\",\"\",124],[\"f340\",\"\",62],[\"f380\",\"\",124],[\"f440\",\"\",62],[\"f480\",\"\",124],[\"f540\",\"\",62],[\"f580\",\"\",124],[\"f640\",\"\",62],[\"f680\",\"\",124],[\"f740\",\"\",62],[\"f780\",\"\",124],[\"f840\",\"\",62],[\"f880\",\"\",124],[\"f940\",\"\"],[\"fa40\",\"ⅰ\",9,\"Ⅰ\",9,\"￢￤＇＂㈱№℡∵纊褜鍈銈蓜俉炻昱棈鋹曻彅丨仡仼伀伃伹佖侒侊侚侔俍偀倢俿倞偆偰偂傔僴僘兊\"],[\"fa80\",\"兤冝冾凬刕劜劦勀勛匀匇匤卲厓厲叝﨎咜咊咩哿喆坙坥垬埈埇﨏塚增墲夋奓奛奝奣妤妺孖寀甯寘寬尞岦岺峵崧嵓﨑嵂嵭嶸嶹巐弡弴彧德忞恝悅悊惞惕愠惲愑愷愰憘戓抦揵摠撝擎敎昀昕昻昉昮昞昤晥晗晙晴晳暙暠暲暿曺朎朗杦枻桒柀栁桄棏﨓楨﨔榘槢樰橫橆橳橾櫢櫤毖氿汜沆汯泚洄涇浯\"],[\"fb40\",\"涖涬淏淸淲淼渹湜渧渼溿澈澵濵瀅瀇瀨炅炫焏焄煜煆煇凞燁燾犱犾猤猪獷玽珉珖珣珒琇珵琦琪琩琮瑢璉璟甁畯皂皜皞皛皦益睆劯砡硎硤硺礰礼神\"],[\"fb80\",\"祥禔福禛竑竧靖竫箞精絈絜綷綠緖繒罇羡羽茁荢荿菇菶葈蒴蕓蕙蕫﨟薰蘒﨡蠇裵訒訷詹誧誾諟諸諶譓譿賰賴贒赶﨣軏﨤逸遧郞都鄕鄧釚釗釞釭釮釤釥鈆鈐鈊鈺鉀鈼鉎鉙鉑鈹鉧銧鉷鉸鋧鋗鋙鋐﨧鋕鋠鋓錥錡鋻﨨錞鋿錝錂鍰鍗鎤鏆鏞鏸鐱鑅鑈閒隆﨩隝隯霳霻靃靍靏靑靕顗顥飯飼餧館馞驎髙\"],[\"fc40\",\"髜魵魲鮏鮱鮻鰀鵰鵫鶴鸙黑\"]]");

/***/ }),
/* 401 */
/***/ (function(module) {

module.exports = JSON.parse("[[\"0\",\"\\u0000\",127],[\"8ea1\",\"｡\",62],[\"a1a1\",\"　、。，．・：；？！゛゜´｀¨＾￣＿ヽヾゝゞ〃仝々〆〇ー―‐／＼～∥｜…‥‘’“”（）〔〕［］｛｝〈\",9,\"＋－±×÷＝≠＜＞≦≧∞∴♂♀°′″℃￥＄￠￡％＃＆＊＠§☆★○●◎◇\"],[\"a2a1\",\"◆□■△▲▽▼※〒→←↑↓〓\"],[\"a2ba\",\"∈∋⊆⊇⊂⊃∪∩\"],[\"a2ca\",\"∧∨￢⇒⇔∀∃\"],[\"a2dc\",\"∠⊥⌒∂∇≡≒≪≫√∽∝∵∫∬\"],[\"a2f2\",\"Å‰♯♭♪†‡¶\"],[\"a2fe\",\"◯\"],[\"a3b0\",\"０\",9],[\"a3c1\",\"Ａ\",25],[\"a3e1\",\"ａ\",25],[\"a4a1\",\"ぁ\",82],[\"a5a1\",\"ァ\",85],[\"a6a1\",\"Α\",16,\"Σ\",6],[\"a6c1\",\"α\",16,\"σ\",6],[\"a7a1\",\"А\",5,\"ЁЖ\",25],[\"a7d1\",\"а\",5,\"ёж\",25],[\"a8a1\",\"─│┌┐┘└├┬┤┴┼━┃┏┓┛┗┣┳┫┻╋┠┯┨┷┿┝┰┥┸╂\"],[\"ada1\",\"①\",19,\"Ⅰ\",9],[\"adc0\",\"㍉㌔㌢㍍㌘㌧㌃㌶㍑㍗㌍㌦㌣㌫㍊㌻㎜㎝㎞㎎㎏㏄㎡\"],[\"addf\",\"㍻〝〟№㏍℡㊤\",4,\"㈱㈲㈹㍾㍽㍼≒≡∫∮∑√⊥∠∟⊿∵∩∪\"],[\"b0a1\",\"亜唖娃阿哀愛挨姶逢葵茜穐悪握渥旭葦芦鯵梓圧斡扱宛姐虻飴絢綾鮎或粟袷安庵按暗案闇鞍杏以伊位依偉囲夷委威尉惟意慰易椅為畏異移維緯胃萎衣謂違遺医井亥域育郁磯一壱溢逸稲茨芋鰯允印咽員因姻引飲淫胤蔭\"],[\"b1a1\",\"院陰隠韻吋右宇烏羽迂雨卯鵜窺丑碓臼渦嘘唄欝蔚鰻姥厩浦瓜閏噂云運雲荏餌叡営嬰影映曳栄永泳洩瑛盈穎頴英衛詠鋭液疫益駅悦謁越閲榎厭円園堰奄宴延怨掩援沿演炎焔煙燕猿縁艶苑薗遠鉛鴛塩於汚甥凹央奥往応\"],[\"b2a1\",\"押旺横欧殴王翁襖鴬鴎黄岡沖荻億屋憶臆桶牡乙俺卸恩温穏音下化仮何伽価佳加可嘉夏嫁家寡科暇果架歌河火珂禍禾稼箇花苛茄荷華菓蝦課嘩貨迦過霞蚊俄峨我牙画臥芽蛾賀雅餓駕介会解回塊壊廻快怪悔恢懐戒拐改\"],[\"b3a1\",\"魁晦械海灰界皆絵芥蟹開階貝凱劾外咳害崖慨概涯碍蓋街該鎧骸浬馨蛙垣柿蛎鈎劃嚇各廓拡撹格核殻獲確穫覚角赫較郭閣隔革学岳楽額顎掛笠樫橿梶鰍潟割喝恰括活渇滑葛褐轄且鰹叶椛樺鞄株兜竃蒲釜鎌噛鴨栢茅萱\"],[\"b4a1\",\"粥刈苅瓦乾侃冠寒刊勘勧巻喚堪姦完官寛干幹患感慣憾換敢柑桓棺款歓汗漢澗潅環甘監看竿管簡緩缶翰肝艦莞観諌貫還鑑間閑関陥韓館舘丸含岸巌玩癌眼岩翫贋雁頑顔願企伎危喜器基奇嬉寄岐希幾忌揮机旗既期棋棄\"],[\"b5a1\",\"機帰毅気汽畿祈季稀紀徽規記貴起軌輝飢騎鬼亀偽儀妓宜戯技擬欺犠疑祇義蟻誼議掬菊鞠吉吃喫桔橘詰砧杵黍却客脚虐逆丘久仇休及吸宮弓急救朽求汲泣灸球究窮笈級糾給旧牛去居巨拒拠挙渠虚許距鋸漁禦魚亨享京\"],[\"b6a1\",\"供侠僑兇競共凶協匡卿叫喬境峡強彊怯恐恭挟教橋況狂狭矯胸脅興蕎郷鏡響饗驚仰凝尭暁業局曲極玉桐粁僅勤均巾錦斤欣欽琴禁禽筋緊芹菌衿襟謹近金吟銀九倶句区狗玖矩苦躯駆駈駒具愚虞喰空偶寓遇隅串櫛釧屑屈\"],[\"b7a1\",\"掘窟沓靴轡窪熊隈粂栗繰桑鍬勲君薫訓群軍郡卦袈祁係傾刑兄啓圭珪型契形径恵慶慧憩掲携敬景桂渓畦稽系経継繋罫茎荊蛍計詣警軽頚鶏芸迎鯨劇戟撃激隙桁傑欠決潔穴結血訣月件倹倦健兼券剣喧圏堅嫌建憲懸拳捲\"],[\"b8a1\",\"検権牽犬献研硯絹県肩見謙賢軒遣鍵険顕験鹸元原厳幻弦減源玄現絃舷言諺限乎個古呼固姑孤己庫弧戸故枯湖狐糊袴股胡菰虎誇跨鈷雇顧鼓五互伍午呉吾娯後御悟梧檎瑚碁語誤護醐乞鯉交佼侯候倖光公功効勾厚口向\"],[\"b9a1\",\"后喉坑垢好孔孝宏工巧巷幸広庚康弘恒慌抗拘控攻昂晃更杭校梗構江洪浩港溝甲皇硬稿糠紅紘絞綱耕考肯肱腔膏航荒行衡講貢購郊酵鉱砿鋼閤降項香高鴻剛劫号合壕拷濠豪轟麹克刻告国穀酷鵠黒獄漉腰甑忽惚骨狛込\"],[\"baa1\",\"此頃今困坤墾婚恨懇昏昆根梱混痕紺艮魂些佐叉唆嵯左差査沙瑳砂詐鎖裟坐座挫債催再最哉塞妻宰彩才採栽歳済災采犀砕砦祭斎細菜裁載際剤在材罪財冴坂阪堺榊肴咲崎埼碕鷺作削咋搾昨朔柵窄策索錯桜鮭笹匙冊刷\"],[\"bba1\",\"察拶撮擦札殺薩雑皐鯖捌錆鮫皿晒三傘参山惨撒散桟燦珊産算纂蚕讃賛酸餐斬暫残仕仔伺使刺司史嗣四士始姉姿子屍市師志思指支孜斯施旨枝止死氏獅祉私糸紙紫肢脂至視詞詩試誌諮資賜雌飼歯事似侍児字寺慈持時\"],[\"bca1\",\"次滋治爾璽痔磁示而耳自蒔辞汐鹿式識鴫竺軸宍雫七叱執失嫉室悉湿漆疾質実蔀篠偲柴芝屡蕊縞舎写射捨赦斜煮社紗者謝車遮蛇邪借勺尺杓灼爵酌釈錫若寂弱惹主取守手朱殊狩珠種腫趣酒首儒受呪寿授樹綬需囚収周\"],[\"bda1\",\"宗就州修愁拾洲秀秋終繍習臭舟蒐衆襲讐蹴輯週酋酬集醜什住充十従戎柔汁渋獣縦重銃叔夙宿淑祝縮粛塾熟出術述俊峻春瞬竣舜駿准循旬楯殉淳準潤盾純巡遵醇順処初所暑曙渚庶緒署書薯藷諸助叙女序徐恕鋤除傷償\"],[\"bea1\",\"勝匠升召哨商唱嘗奨妾娼宵将小少尚庄床廠彰承抄招掌捷昇昌昭晶松梢樟樵沼消渉湘焼焦照症省硝礁祥称章笑粧紹肖菖蒋蕉衝裳訟証詔詳象賞醤鉦鍾鐘障鞘上丈丞乗冗剰城場壌嬢常情擾条杖浄状畳穣蒸譲醸錠嘱埴飾\"],[\"bfa1\",\"拭植殖燭織職色触食蝕辱尻伸信侵唇娠寝審心慎振新晋森榛浸深申疹真神秦紳臣芯薪親診身辛進針震人仁刃塵壬尋甚尽腎訊迅陣靭笥諏須酢図厨逗吹垂帥推水炊睡粋翠衰遂酔錐錘随瑞髄崇嵩数枢趨雛据杉椙菅頗雀裾\"],[\"c0a1\",\"澄摺寸世瀬畝是凄制勢姓征性成政整星晴棲栖正清牲生盛精聖声製西誠誓請逝醒青静斉税脆隻席惜戚斥昔析石積籍績脊責赤跡蹟碩切拙接摂折設窃節説雪絶舌蝉仙先千占宣専尖川戦扇撰栓栴泉浅洗染潜煎煽旋穿箭線\"],[\"c1a1\",\"繊羨腺舛船薦詮賎践選遷銭銑閃鮮前善漸然全禅繕膳糎噌塑岨措曾曽楚狙疏疎礎祖租粗素組蘇訴阻遡鼠僧創双叢倉喪壮奏爽宋層匝惣想捜掃挿掻操早曹巣槍槽漕燥争痩相窓糟総綜聡草荘葬蒼藻装走送遭鎗霜騒像増憎\"],[\"c2a1\",\"臓蔵贈造促側則即息捉束測足速俗属賊族続卒袖其揃存孫尊損村遜他多太汰詑唾堕妥惰打柁舵楕陀駄騨体堆対耐岱帯待怠態戴替泰滞胎腿苔袋貸退逮隊黛鯛代台大第醍題鷹滝瀧卓啄宅托択拓沢濯琢託鐸濁諾茸凧蛸只\"],[\"c3a1\",\"叩但達辰奪脱巽竪辿棚谷狸鱈樽誰丹単嘆坦担探旦歎淡湛炭短端箪綻耽胆蛋誕鍛団壇弾断暖檀段男談値知地弛恥智池痴稚置致蜘遅馳築畜竹筑蓄逐秩窒茶嫡着中仲宙忠抽昼柱注虫衷註酎鋳駐樗瀦猪苧著貯丁兆凋喋寵\"],[\"c4a1\",\"帖帳庁弔張彫徴懲挑暢朝潮牒町眺聴脹腸蝶調諜超跳銚長頂鳥勅捗直朕沈珍賃鎮陳津墜椎槌追鎚痛通塚栂掴槻佃漬柘辻蔦綴鍔椿潰坪壷嬬紬爪吊釣鶴亭低停偵剃貞呈堤定帝底庭廷弟悌抵挺提梯汀碇禎程締艇訂諦蹄逓\"],[\"c5a1\",\"邸鄭釘鼎泥摘擢敵滴的笛適鏑溺哲徹撤轍迭鉄典填天展店添纏甜貼転顛点伝殿澱田電兎吐堵塗妬屠徒斗杜渡登菟賭途都鍍砥砺努度土奴怒倒党冬凍刀唐塔塘套宕島嶋悼投搭東桃梼棟盗淘湯涛灯燈当痘祷等答筒糖統到\"],[\"c6a1\",\"董蕩藤討謄豆踏逃透鐙陶頭騰闘働動同堂導憧撞洞瞳童胴萄道銅峠鴇匿得徳涜特督禿篤毒独読栃橡凸突椴届鳶苫寅酉瀞噸屯惇敦沌豚遁頓呑曇鈍奈那内乍凪薙謎灘捺鍋楢馴縄畷南楠軟難汝二尼弐迩匂賑肉虹廿日乳入\"],[\"c7a1\",\"如尿韮任妊忍認濡禰祢寧葱猫熱年念捻撚燃粘乃廼之埜嚢悩濃納能脳膿農覗蚤巴把播覇杷波派琶破婆罵芭馬俳廃拝排敗杯盃牌背肺輩配倍培媒梅楳煤狽買売賠陪這蝿秤矧萩伯剥博拍柏泊白箔粕舶薄迫曝漠爆縛莫駁麦\"],[\"c8a1\",\"函箱硲箸肇筈櫨幡肌畑畠八鉢溌発醗髪伐罰抜筏閥鳩噺塙蛤隼伴判半反叛帆搬斑板氾汎版犯班畔繁般藩販範釆煩頒飯挽晩番盤磐蕃蛮匪卑否妃庇彼悲扉批披斐比泌疲皮碑秘緋罷肥被誹費避非飛樋簸備尾微枇毘琵眉美\"],[\"c9a1\",\"鼻柊稗匹疋髭彦膝菱肘弼必畢筆逼桧姫媛紐百謬俵彪標氷漂瓢票表評豹廟描病秒苗錨鋲蒜蛭鰭品彬斌浜瀕貧賓頻敏瓶不付埠夫婦富冨布府怖扶敷斧普浮父符腐膚芙譜負賦赴阜附侮撫武舞葡蕪部封楓風葺蕗伏副復幅服\"],[\"caa1\",\"福腹複覆淵弗払沸仏物鮒分吻噴墳憤扮焚奮粉糞紛雰文聞丙併兵塀幣平弊柄並蔽閉陛米頁僻壁癖碧別瞥蔑箆偏変片篇編辺返遍便勉娩弁鞭保舗鋪圃捕歩甫補輔穂募墓慕戊暮母簿菩倣俸包呆報奉宝峰峯崩庖抱捧放方朋\"],[\"cba1\",\"法泡烹砲縫胞芳萌蓬蜂褒訪豊邦鋒飽鳳鵬乏亡傍剖坊妨帽忘忙房暴望某棒冒紡肪膨謀貌貿鉾防吠頬北僕卜墨撲朴牧睦穆釦勃没殆堀幌奔本翻凡盆摩磨魔麻埋妹昧枚毎哩槙幕膜枕鮪柾鱒桝亦俣又抹末沫迄侭繭麿万慢満\"],[\"cca1\",\"漫蔓味未魅巳箕岬密蜜湊蓑稔脈妙粍民眠務夢無牟矛霧鵡椋婿娘冥名命明盟迷銘鳴姪牝滅免棉綿緬面麺摸模茂妄孟毛猛盲網耗蒙儲木黙目杢勿餅尤戻籾貰問悶紋門匁也冶夜爺耶野弥矢厄役約薬訳躍靖柳薮鑓愉愈油癒\"],[\"cda1\",\"諭輸唯佑優勇友宥幽悠憂揖有柚湧涌猶猷由祐裕誘遊邑郵雄融夕予余与誉輿預傭幼妖容庸揚揺擁曜楊様洋溶熔用窯羊耀葉蓉要謡踊遥陽養慾抑欲沃浴翌翼淀羅螺裸来莱頼雷洛絡落酪乱卵嵐欄濫藍蘭覧利吏履李梨理璃\"],[\"cea1\",\"痢裏裡里離陸律率立葎掠略劉流溜琉留硫粒隆竜龍侶慮旅虜了亮僚両凌寮料梁涼猟療瞭稜糧良諒遼量陵領力緑倫厘林淋燐琳臨輪隣鱗麟瑠塁涙累類令伶例冷励嶺怜玲礼苓鈴隷零霊麗齢暦歴列劣烈裂廉恋憐漣煉簾練聯\"],[\"cfa1\",\"蓮連錬呂魯櫓炉賂路露労婁廊弄朗楼榔浪漏牢狼篭老聾蝋郎六麓禄肋録論倭和話歪賄脇惑枠鷲亙亘鰐詫藁蕨椀湾碗腕\"],[\"d0a1\",\"弌丐丕个丱丶丼丿乂乖乘亂亅豫亊舒弍于亞亟亠亢亰亳亶从仍仄仆仂仗仞仭仟价伉佚估佛佝佗佇佶侈侏侘佻佩佰侑佯來侖儘俔俟俎俘俛俑俚俐俤俥倚倨倔倪倥倅伜俶倡倩倬俾俯們倆偃假會偕偐偈做偖偬偸傀傚傅傴傲\"],[\"d1a1\",\"僉僊傳僂僖僞僥僭僣僮價僵儉儁儂儖儕儔儚儡儺儷儼儻儿兀兒兌兔兢竸兩兪兮冀冂囘册冉冏冑冓冕冖冤冦冢冩冪冫决冱冲冰况冽凅凉凛几處凩凭凰凵凾刄刋刔刎刧刪刮刳刹剏剄剋剌剞剔剪剴剩剳剿剽劍劔劒剱劈劑辨\"],[\"d2a1\",\"辧劬劭劼劵勁勍勗勞勣勦飭勠勳勵勸勹匆匈甸匍匐匏匕匚匣匯匱匳匸區卆卅丗卉卍凖卞卩卮夘卻卷厂厖厠厦厥厮厰厶參簒雙叟曼燮叮叨叭叺吁吽呀听吭吼吮吶吩吝呎咏呵咎呟呱呷呰咒呻咀呶咄咐咆哇咢咸咥咬哄哈咨\"],[\"d3a1\",\"咫哂咤咾咼哘哥哦唏唔哽哮哭哺哢唹啀啣啌售啜啅啖啗唸唳啝喙喀咯喊喟啻啾喘喞單啼喃喩喇喨嗚嗅嗟嗄嗜嗤嗔嘔嗷嘖嗾嗽嘛嗹噎噐營嘴嘶嘲嘸噫噤嘯噬噪嚆嚀嚊嚠嚔嚏嚥嚮嚶嚴囂嚼囁囃囀囈囎囑囓囗囮囹圀囿圄圉\"],[\"d4a1\",\"圈國圍圓團圖嗇圜圦圷圸坎圻址坏坩埀垈坡坿垉垓垠垳垤垪垰埃埆埔埒埓堊埖埣堋堙堝塲堡塢塋塰毀塒堽塹墅墹墟墫墺壞墻墸墮壅壓壑壗壙壘壥壜壤壟壯壺壹壻壼壽夂夊夐夛梦夥夬夭夲夸夾竒奕奐奎奚奘奢奠奧奬奩\"],[\"d5a1\",\"奸妁妝佞侫妣妲姆姨姜妍姙姚娥娟娑娜娉娚婀婬婉娵娶婢婪媚媼媾嫋嫂媽嫣嫗嫦嫩嫖嫺嫻嬌嬋嬖嬲嫐嬪嬶嬾孃孅孀孑孕孚孛孥孩孰孳孵學斈孺宀它宦宸寃寇寉寔寐寤實寢寞寥寫寰寶寳尅將專對尓尠尢尨尸尹屁屆屎屓\"],[\"d6a1\",\"屐屏孱屬屮乢屶屹岌岑岔妛岫岻岶岼岷峅岾峇峙峩峽峺峭嶌峪崋崕崗嵜崟崛崑崔崢崚崙崘嵌嵒嵎嵋嵬嵳嵶嶇嶄嶂嶢嶝嶬嶮嶽嶐嶷嶼巉巍巓巒巖巛巫已巵帋帚帙帑帛帶帷幄幃幀幎幗幔幟幢幤幇幵并幺麼广庠廁廂廈廐廏\"],[\"d7a1\",\"廖廣廝廚廛廢廡廨廩廬廱廳廰廴廸廾弃弉彝彜弋弑弖弩弭弸彁彈彌彎弯彑彖彗彙彡彭彳彷徃徂彿徊很徑徇從徙徘徠徨徭徼忖忻忤忸忱忝悳忿怡恠怙怐怩怎怱怛怕怫怦怏怺恚恁恪恷恟恊恆恍恣恃恤恂恬恫恙悁悍惧悃悚\"],[\"d8a1\",\"悄悛悖悗悒悧悋惡悸惠惓悴忰悽惆悵惘慍愕愆惶惷愀惴惺愃愡惻惱愍愎慇愾愨愧慊愿愼愬愴愽慂慄慳慷慘慙慚慫慴慯慥慱慟慝慓慵憙憖憇憬憔憚憊憑憫憮懌懊應懷懈懃懆憺懋罹懍懦懣懶懺懴懿懽懼懾戀戈戉戍戌戔戛\"],[\"d9a1\",\"戞戡截戮戰戲戳扁扎扞扣扛扠扨扼抂抉找抒抓抖拔抃抔拗拑抻拏拿拆擔拈拜拌拊拂拇抛拉挌拮拱挧挂挈拯拵捐挾捍搜捏掖掎掀掫捶掣掏掉掟掵捫捩掾揩揀揆揣揉插揶揄搖搴搆搓搦搶攝搗搨搏摧摯摶摎攪撕撓撥撩撈撼\"],[\"daa1\",\"據擒擅擇撻擘擂擱擧舉擠擡抬擣擯攬擶擴擲擺攀擽攘攜攅攤攣攫攴攵攷收攸畋效敖敕敍敘敞敝敲數斂斃變斛斟斫斷旃旆旁旄旌旒旛旙无旡旱杲昊昃旻杳昵昶昴昜晏晄晉晁晞晝晤晧晨晟晢晰暃暈暎暉暄暘暝曁暹曉暾暼\"],[\"dba1\",\"曄暸曖曚曠昿曦曩曰曵曷朏朖朞朦朧霸朮朿朶杁朸朷杆杞杠杙杣杤枉杰枩杼杪枌枋枦枡枅枷柯枴柬枳柩枸柤柞柝柢柮枹柎柆柧檜栞框栩桀桍栲桎梳栫桙档桷桿梟梏梭梔條梛梃檮梹桴梵梠梺椏梍桾椁棊椈棘椢椦棡椌棍\"],[\"dca1\",\"棔棧棕椶椒椄棗棣椥棹棠棯椨椪椚椣椡棆楹楷楜楸楫楔楾楮椹楴椽楙椰楡楞楝榁楪榲榮槐榿槁槓榾槎寨槊槝榻槃榧樮榑榠榜榕榴槞槨樂樛槿權槹槲槧樅榱樞槭樔槫樊樒櫁樣樓橄樌橲樶橸橇橢橙橦橈樸樢檐檍檠檄檢檣\"],[\"dda1\",\"檗蘗檻櫃櫂檸檳檬櫞櫑櫟檪櫚櫪櫻欅蘖櫺欒欖鬱欟欸欷盜欹飮歇歃歉歐歙歔歛歟歡歸歹歿殀殄殃殍殘殕殞殤殪殫殯殲殱殳殷殼毆毋毓毟毬毫毳毯麾氈氓气氛氤氣汞汕汢汪沂沍沚沁沛汾汨汳沒沐泄泱泓沽泗泅泝沮沱沾\"],[\"dea1\",\"沺泛泯泙泪洟衍洶洫洽洸洙洵洳洒洌浣涓浤浚浹浙涎涕濤涅淹渕渊涵淇淦涸淆淬淞淌淨淒淅淺淙淤淕淪淮渭湮渮渙湲湟渾渣湫渫湶湍渟湃渺湎渤滿渝游溂溪溘滉溷滓溽溯滄溲滔滕溏溥滂溟潁漑灌滬滸滾漿滲漱滯漲滌\"],[\"dfa1\",\"漾漓滷澆潺潸澁澀潯潛濳潭澂潼潘澎澑濂潦澳澣澡澤澹濆澪濟濕濬濔濘濱濮濛瀉瀋濺瀑瀁瀏濾瀛瀚潴瀝瀘瀟瀰瀾瀲灑灣炙炒炯烱炬炸炳炮烟烋烝烙焉烽焜焙煥煕熈煦煢煌煖煬熏燻熄熕熨熬燗熹熾燒燉燔燎燠燬燧燵燼\"],[\"e0a1\",\"燹燿爍爐爛爨爭爬爰爲爻爼爿牀牆牋牘牴牾犂犁犇犒犖犢犧犹犲狃狆狄狎狒狢狠狡狹狷倏猗猊猜猖猝猴猯猩猥猾獎獏默獗獪獨獰獸獵獻獺珈玳珎玻珀珥珮珞璢琅瑯琥珸琲琺瑕琿瑟瑙瑁瑜瑩瑰瑣瑪瑶瑾璋璞璧瓊瓏瓔珱\"],[\"e1a1\",\"瓠瓣瓧瓩瓮瓲瓰瓱瓸瓷甄甃甅甌甎甍甕甓甞甦甬甼畄畍畊畉畛畆畚畩畤畧畫畭畸當疆疇畴疊疉疂疔疚疝疥疣痂疳痃疵疽疸疼疱痍痊痒痙痣痞痾痿痼瘁痰痺痲痳瘋瘍瘉瘟瘧瘠瘡瘢瘤瘴瘰瘻癇癈癆癜癘癡癢癨癩癪癧癬癰\"],[\"e2a1\",\"癲癶癸發皀皃皈皋皎皖皓皙皚皰皴皸皹皺盂盍盖盒盞盡盥盧盪蘯盻眈眇眄眩眤眞眥眦眛眷眸睇睚睨睫睛睥睿睾睹瞎瞋瞑瞠瞞瞰瞶瞹瞿瞼瞽瞻矇矍矗矚矜矣矮矼砌砒礦砠礪硅碎硴碆硼碚碌碣碵碪碯磑磆磋磔碾碼磅磊磬\"],[\"e3a1\",\"磧磚磽磴礇礒礑礙礬礫祀祠祗祟祚祕祓祺祿禊禝禧齋禪禮禳禹禺秉秕秧秬秡秣稈稍稘稙稠稟禀稱稻稾稷穃穗穉穡穢穩龝穰穹穽窈窗窕窘窖窩竈窰窶竅竄窿邃竇竊竍竏竕竓站竚竝竡竢竦竭竰笂笏笊笆笳笘笙笞笵笨笶筐\"],[\"e4a1\",\"筺笄筍笋筌筅筵筥筴筧筰筱筬筮箝箘箟箍箜箚箋箒箏筝箙篋篁篌篏箴篆篝篩簑簔篦篥籠簀簇簓篳篷簗簍篶簣簧簪簟簷簫簽籌籃籔籏籀籐籘籟籤籖籥籬籵粃粐粤粭粢粫粡粨粳粲粱粮粹粽糀糅糂糘糒糜糢鬻糯糲糴糶糺紆\"],[\"e5a1\",\"紂紜紕紊絅絋紮紲紿紵絆絳絖絎絲絨絮絏絣經綉絛綏絽綛綺綮綣綵緇綽綫總綢綯緜綸綟綰緘緝緤緞緻緲緡縅縊縣縡縒縱縟縉縋縢繆繦縻縵縹繃縷縲縺繧繝繖繞繙繚繹繪繩繼繻纃緕繽辮繿纈纉續纒纐纓纔纖纎纛纜缸缺\"],[\"e6a1\",\"罅罌罍罎罐网罕罔罘罟罠罨罩罧罸羂羆羃羈羇羌羔羞羝羚羣羯羲羹羮羶羸譱翅翆翊翕翔翡翦翩翳翹飜耆耄耋耒耘耙耜耡耨耿耻聊聆聒聘聚聟聢聨聳聲聰聶聹聽聿肄肆肅肛肓肚肭冐肬胛胥胙胝胄胚胖脉胯胱脛脩脣脯腋\"],[\"e7a1\",\"隋腆脾腓腑胼腱腮腥腦腴膃膈膊膀膂膠膕膤膣腟膓膩膰膵膾膸膽臀臂膺臉臍臑臙臘臈臚臟臠臧臺臻臾舁舂舅與舊舍舐舖舩舫舸舳艀艙艘艝艚艟艤艢艨艪艫舮艱艷艸艾芍芒芫芟芻芬苡苣苟苒苴苳苺莓范苻苹苞茆苜茉苙\"],[\"e8a1\",\"茵茴茖茲茱荀茹荐荅茯茫茗茘莅莚莪莟莢莖茣莎莇莊荼莵荳荵莠莉莨菴萓菫菎菽萃菘萋菁菷萇菠菲萍萢萠莽萸蔆菻葭萪萼蕚蒄葷葫蒭葮蒂葩葆萬葯葹萵蓊葢蒹蒿蒟蓙蓍蒻蓚蓐蓁蓆蓖蒡蔡蓿蓴蔗蔘蔬蔟蔕蔔蓼蕀蕣蕘蕈\"],[\"e9a1\",\"蕁蘂蕋蕕薀薤薈薑薊薨蕭薔薛藪薇薜蕷蕾薐藉薺藏薹藐藕藝藥藜藹蘊蘓蘋藾藺蘆蘢蘚蘰蘿虍乕虔號虧虱蚓蚣蚩蚪蚋蚌蚶蚯蛄蛆蚰蛉蠣蚫蛔蛞蛩蛬蛟蛛蛯蜒蜆蜈蜀蜃蛻蜑蜉蜍蛹蜊蜴蜿蜷蜻蜥蜩蜚蝠蝟蝸蝌蝎蝴蝗蝨蝮蝙\"],[\"eaa1\",\"蝓蝣蝪蠅螢螟螂螯蟋螽蟀蟐雖螫蟄螳蟇蟆螻蟯蟲蟠蠏蠍蟾蟶蟷蠎蟒蠑蠖蠕蠢蠡蠱蠶蠹蠧蠻衄衂衒衙衞衢衫袁衾袞衵衽袵衲袂袗袒袮袙袢袍袤袰袿袱裃裄裔裘裙裝裹褂裼裴裨裲褄褌褊褓襃褞褥褪褫襁襄褻褶褸襌褝襠襞\"],[\"eba1\",\"襦襤襭襪襯襴襷襾覃覈覊覓覘覡覩覦覬覯覲覺覽覿觀觚觜觝觧觴觸訃訖訐訌訛訝訥訶詁詛詒詆詈詼詭詬詢誅誂誄誨誡誑誥誦誚誣諄諍諂諚諫諳諧諤諱謔諠諢諷諞諛謌謇謚諡謖謐謗謠謳鞫謦謫謾謨譁譌譏譎證譖譛譚譫\"],[\"eca1\",\"譟譬譯譴譽讀讌讎讒讓讖讙讚谺豁谿豈豌豎豐豕豢豬豸豺貂貉貅貊貍貎貔豼貘戝貭貪貽貲貳貮貶賈賁賤賣賚賽賺賻贄贅贊贇贏贍贐齎贓賍贔贖赧赭赱赳趁趙跂趾趺跏跚跖跌跛跋跪跫跟跣跼踈踉跿踝踞踐踟蹂踵踰踴蹊\"],[\"eda1\",\"蹇蹉蹌蹐蹈蹙蹤蹠踪蹣蹕蹶蹲蹼躁躇躅躄躋躊躓躑躔躙躪躡躬躰軆躱躾軅軈軋軛軣軼軻軫軾輊輅輕輒輙輓輜輟輛輌輦輳輻輹轅轂輾轌轉轆轎轗轜轢轣轤辜辟辣辭辯辷迚迥迢迪迯邇迴逅迹迺逑逕逡逍逞逖逋逧逶逵逹迸\"],[\"eea1\",\"遏遐遑遒逎遉逾遖遘遞遨遯遶隨遲邂遽邁邀邊邉邏邨邯邱邵郢郤扈郛鄂鄒鄙鄲鄰酊酖酘酣酥酩酳酲醋醉醂醢醫醯醪醵醴醺釀釁釉釋釐釖釟釡釛釼釵釶鈞釿鈔鈬鈕鈑鉞鉗鉅鉉鉤鉈銕鈿鉋鉐銜銖銓銛鉚鋏銹銷鋩錏鋺鍄錮\"],[\"efa1\",\"錙錢錚錣錺錵錻鍜鍠鍼鍮鍖鎰鎬鎭鎔鎹鏖鏗鏨鏥鏘鏃鏝鏐鏈鏤鐚鐔鐓鐃鐇鐐鐶鐫鐵鐡鐺鑁鑒鑄鑛鑠鑢鑞鑪鈩鑰鑵鑷鑽鑚鑼鑾钁鑿閂閇閊閔閖閘閙閠閨閧閭閼閻閹閾闊濶闃闍闌闕闔闖關闡闥闢阡阨阮阯陂陌陏陋陷陜陞\"],[\"f0a1\",\"陝陟陦陲陬隍隘隕隗險隧隱隲隰隴隶隸隹雎雋雉雍襍雜霍雕雹霄霆霈霓霎霑霏霖霙霤霪霰霹霽霾靄靆靈靂靉靜靠靤靦靨勒靫靱靹鞅靼鞁靺鞆鞋鞏鞐鞜鞨鞦鞣鞳鞴韃韆韈韋韜韭齏韲竟韶韵頏頌頸頤頡頷頽顆顏顋顫顯顰\"],[\"f1a1\",\"顱顴顳颪颯颱颶飄飃飆飩飫餃餉餒餔餘餡餝餞餤餠餬餮餽餾饂饉饅饐饋饑饒饌饕馗馘馥馭馮馼駟駛駝駘駑駭駮駱駲駻駸騁騏騅駢騙騫騷驅驂驀驃騾驕驍驛驗驟驢驥驤驩驫驪骭骰骼髀髏髑髓體髞髟髢髣髦髯髫髮髴髱髷\"],[\"f2a1\",\"髻鬆鬘鬚鬟鬢鬣鬥鬧鬨鬩鬪鬮鬯鬲魄魃魏魍魎魑魘魴鮓鮃鮑鮖鮗鮟鮠鮨鮴鯀鯊鮹鯆鯏鯑鯒鯣鯢鯤鯔鯡鰺鯲鯱鯰鰕鰔鰉鰓鰌鰆鰈鰒鰊鰄鰮鰛鰥鰤鰡鰰鱇鰲鱆鰾鱚鱠鱧鱶鱸鳧鳬鳰鴉鴈鳫鴃鴆鴪鴦鶯鴣鴟鵄鴕鴒鵁鴿鴾鵆鵈\"],[\"f3a1\",\"鵝鵞鵤鵑鵐鵙鵲鶉鶇鶫鵯鵺鶚鶤鶩鶲鷄鷁鶻鶸鶺鷆鷏鷂鷙鷓鷸鷦鷭鷯鷽鸚鸛鸞鹵鹹鹽麁麈麋麌麒麕麑麝麥麩麸麪麭靡黌黎黏黐黔黜點黝黠黥黨黯黴黶黷黹黻黼黽鼇鼈皷鼕鼡鼬鼾齊齒齔齣齟齠齡齦齧齬齪齷齲齶龕龜龠\"],[\"f4a1\",\"堯槇遙瑤凜熙\"],[\"f9a1\",\"纊褜鍈銈蓜俉炻昱棈鋹曻彅丨仡仼伀伃伹佖侒侊侚侔俍偀倢俿倞偆偰偂傔僴僘兊兤冝冾凬刕劜劦勀勛匀匇匤卲厓厲叝﨎咜咊咩哿喆坙坥垬埈埇﨏塚增墲夋奓奛奝奣妤妺孖寀甯寘寬尞岦岺峵崧嵓﨑嵂嵭嶸嶹巐弡弴彧德\"],[\"faa1\",\"忞恝悅悊惞惕愠惲愑愷愰憘戓抦揵摠撝擎敎昀昕昻昉昮昞昤晥晗晙晴晳暙暠暲暿曺朎朗杦枻桒柀栁桄棏﨓楨﨔榘槢樰橫橆橳橾櫢櫤毖氿汜沆汯泚洄涇浯涖涬淏淸淲淼渹湜渧渼溿澈澵濵瀅瀇瀨炅炫焏焄煜煆煇凞燁燾犱\"],[\"fba1\",\"犾猤猪獷玽珉珖珣珒琇珵琦琪琩琮瑢璉璟甁畯皂皜皞皛皦益睆劯砡硎硤硺礰礼神祥禔福禛竑竧靖竫箞精絈絜綷綠緖繒罇羡羽茁荢荿菇菶葈蒴蕓蕙蕫﨟薰蘒﨡蠇裵訒訷詹誧誾諟諸諶譓譿賰賴贒赶﨣軏﨤逸遧郞都鄕鄧釚\"],[\"fca1\",\"釗釞釭釮釤釥鈆鈐鈊鈺鉀鈼鉎鉙鉑鈹鉧銧鉷鉸鋧鋗鋙鋐﨧鋕鋠鋓錥錡鋻﨨錞鋿錝錂鍰鍗鎤鏆鏞鏸鐱鑅鑈閒隆﨩隝隯霳霻靃靍靏靑靕顗顥飯飼餧館馞驎髙髜魵魲鮏鮱鮻鰀鵰鵫鶴鸙黑\"],[\"fcf1\",\"ⅰ\",9,\"￢￤＇＂\"],[\"8fa2af\",\"˘ˇ¸˙˝¯˛˚～΄΅\"],[\"8fa2c2\",\"¡¦¿\"],[\"8fa2eb\",\"ºª©®™¤№\"],[\"8fa6e1\",\"ΆΈΉΊΪ\"],[\"8fa6e7\",\"Ό\"],[\"8fa6e9\",\"ΎΫ\"],[\"8fa6ec\",\"Ώ\"],[\"8fa6f1\",\"άέήίϊΐόςύϋΰώ\"],[\"8fa7c2\",\"Ђ\",10,\"ЎЏ\"],[\"8fa7f2\",\"ђ\",10,\"ўџ\"],[\"8fa9a1\",\"ÆĐ\"],[\"8fa9a4\",\"Ħ\"],[\"8fa9a6\",\"Ĳ\"],[\"8fa9a8\",\"ŁĿ\"],[\"8fa9ab\",\"ŊØŒ\"],[\"8fa9af\",\"ŦÞ\"],[\"8fa9c1\",\"æđðħıĳĸłŀŉŋøœßŧþ\"],[\"8faaa1\",\"ÁÀÄÂĂǍĀĄÅÃĆĈČÇĊĎÉÈËÊĚĖĒĘ\"],[\"8faaba\",\"ĜĞĢĠĤÍÌÏÎǏİĪĮĨĴĶĹĽĻŃŇŅÑÓÒÖÔǑŐŌÕŔŘŖŚŜŠŞŤŢÚÙÜÛŬǓŰŪŲŮŨǗǛǙǕŴÝŸŶŹŽŻ\"],[\"8faba1\",\"áàäâăǎāąåãćĉčçċďéèëêěėēęǵĝğ\"],[\"8fabbd\",\"ġĥíìïîǐ\"],[\"8fabc5\",\"īįĩĵķĺľļńňņñóòöôǒőōõŕřŗśŝšşťţúùüûŭǔűūųůũǘǜǚǖŵýÿŷźžż\"],[\"8fb0a1\",\"丂丄丅丌丒丟丣两丨丫丮丯丰丵乀乁乄乇乑乚乜乣乨乩乴乵乹乿亍亖亗亝亯亹仃仐仚仛仠仡仢仨仯仱仳仵份仾仿伀伂伃伈伋伌伒伕伖众伙伮伱你伳伵伷伹伻伾佀佂佈佉佋佌佒佔佖佘佟佣佪佬佮佱佷佸佹佺佽佾侁侂侄\"],[\"8fb1a1\",\"侅侉侊侌侎侐侒侓侔侗侙侚侞侟侲侷侹侻侼侽侾俀俁俅俆俈俉俋俌俍俏俒俜俠俢俰俲俼俽俿倀倁倄倇倊倌倎倐倓倗倘倛倜倝倞倢倧倮倰倲倳倵偀偁偂偅偆偊偌偎偑偒偓偗偙偟偠偢偣偦偧偪偭偰偱倻傁傃傄傆傊傎傏傐\"],[\"8fb2a1\",\"傒傓傔傖傛傜傞\",4,\"傪傯傰傹傺傽僀僃僄僇僌僎僐僓僔僘僜僝僟僢僤僦僨僩僯僱僶僺僾儃儆儇儈儋儌儍儎僲儐儗儙儛儜儝儞儣儧儨儬儭儯儱儳儴儵儸儹兂兊兏兓兕兗兘兟兤兦兾冃冄冋冎冘冝冡冣冭冸冺冼冾冿凂\"],[\"8fb3a1\",\"凈减凑凒凓凕凘凞凢凥凮凲凳凴凷刁刂刅划刓刕刖刘刢刨刱刲刵刼剅剉剕剗剘剚剜剟剠剡剦剮剷剸剹劀劂劅劊劌劓劕劖劗劘劚劜劤劥劦劧劯劰劶劷劸劺劻劽勀勄勆勈勌勏勑勔勖勛勜勡勥勨勩勪勬勰勱勴勶勷匀匃匊匋\"],[\"8fb4a1\",\"匌匑匓匘匛匜匞匟匥匧匨匩匫匬匭匰匲匵匼匽匾卂卌卋卙卛卡卣卥卬卭卲卹卾厃厇厈厎厓厔厙厝厡厤厪厫厯厲厴厵厷厸厺厽叀叅叏叒叓叕叚叝叞叠另叧叵吂吓吚吡吧吨吪启吱吴吵呃呄呇呍呏呞呢呤呦呧呩呫呭呮呴呿\"],[\"8fb5a1\",\"咁咃咅咈咉咍咑咕咖咜咟咡咦咧咩咪咭咮咱咷咹咺咻咿哆哊响哎哠哪哬哯哶哼哾哿唀唁唅唈唉唌唍唎唕唪唫唲唵唶唻唼唽啁啇啉啊啍啐啑啘啚啛啞啠啡啤啦啿喁喂喆喈喎喏喑喒喓喔喗喣喤喭喲喿嗁嗃嗆嗉嗋嗌嗎嗑嗒\"],[\"8fb6a1\",\"嗓嗗嗘嗛嗞嗢嗩嗶嗿嘅嘈嘊嘍\",5,\"嘙嘬嘰嘳嘵嘷嘹嘻嘼嘽嘿噀噁噃噄噆噉噋噍噏噔噞噠噡噢噣噦噩噭噯噱噲噵嚄嚅嚈嚋嚌嚕嚙嚚嚝嚞嚟嚦嚧嚨嚩嚫嚬嚭嚱嚳嚷嚾囅囉囊囋囏囐囌囍囙囜囝囟囡囤\",4,\"囱囫园\"],[\"8fb7a1\",\"囶囷圁圂圇圊圌圑圕圚圛圝圠圢圣圤圥圩圪圬圮圯圳圴圽圾圿坅坆坌坍坒坢坥坧坨坫坭\",4,\"坳坴坵坷坹坺坻坼坾垁垃垌垔垗垙垚垜垝垞垟垡垕垧垨垩垬垸垽埇埈埌埏埕埝埞埤埦埧埩埭埰埵埶埸埽埾埿堃堄堈堉埡\"],[\"8fb8a1\",\"堌堍堛堞堟堠堦堧堭堲堹堿塉塌塍塏塐塕塟塡塤塧塨塸塼塿墀墁墇墈墉墊墌墍墏墐墔墖墝墠墡墢墦墩墱墲壄墼壂壈壍壎壐壒壔壖壚壝壡壢壩壳夅夆夋夌夒夓夔虁夝夡夣夤夨夯夰夳夵夶夿奃奆奒奓奙奛奝奞奟奡奣奫奭\"],[\"8fb9a1\",\"奯奲奵奶她奻奼妋妌妎妒妕妗妟妤妧妭妮妯妰妳妷妺妼姁姃姄姈姊姍姒姝姞姟姣姤姧姮姯姱姲姴姷娀娄娌娍娎娒娓娞娣娤娧娨娪娭娰婄婅婇婈婌婐婕婞婣婥婧婭婷婺婻婾媋媐媓媖媙媜媞媟媠媢媧媬媱媲媳媵媸媺媻媿\"],[\"8fbaa1\",\"嫄嫆嫈嫏嫚嫜嫠嫥嫪嫮嫵嫶嫽嬀嬁嬈嬗嬴嬙嬛嬝嬡嬥嬭嬸孁孋孌孒孖孞孨孮孯孼孽孾孿宁宄宆宊宎宐宑宓宔宖宨宩宬宭宯宱宲宷宺宼寀寁寍寏寖\",4,\"寠寯寱寴寽尌尗尞尟尣尦尩尫尬尮尰尲尵尶屙屚屜屢屣屧屨屩\"],[\"8fbba1\",\"屭屰屴屵屺屻屼屽岇岈岊岏岒岝岟岠岢岣岦岪岲岴岵岺峉峋峒峝峗峮峱峲峴崁崆崍崒崫崣崤崦崧崱崴崹崽崿嵂嵃嵆嵈嵕嵑嵙嵊嵟嵠嵡嵢嵤嵪嵭嵰嵹嵺嵾嵿嶁嶃嶈嶊嶒嶓嶔嶕嶙嶛嶟嶠嶧嶫嶰嶴嶸嶹巃巇巋巐巎巘巙巠巤\"],[\"8fbca1\",\"巩巸巹帀帇帍帒帔帕帘帟帠帮帨帲帵帾幋幐幉幑幖幘幛幜幞幨幪\",4,\"幰庀庋庎庢庤庥庨庪庬庱庳庽庾庿廆廌廋廎廑廒廔廕廜廞廥廫异弆弇弈弎弙弜弝弡弢弣弤弨弫弬弮弰弴弶弻弽弿彀彄彅彇彍彐彔彘彛彠彣彤彧\"],[\"8fbda1\",\"彯彲彴彵彸彺彽彾徉徍徏徖徜徝徢徧徫徤徬徯徰徱徸忄忇忈忉忋忐\",4,\"忞忡忢忨忩忪忬忭忮忯忲忳忶忺忼怇怊怍怓怔怗怘怚怟怤怭怳怵恀恇恈恉恌恑恔恖恗恝恡恧恱恾恿悂悆悈悊悎悑悓悕悘悝悞悢悤悥您悰悱悷\"],[\"8fbea1\",\"悻悾惂惄惈惉惊惋惎惏惔惕惙惛惝惞惢惥惲惵惸惼惽愂愇愊愌愐\",4,\"愖愗愙愜愞愢愪愫愰愱愵愶愷愹慁慅慆慉慞慠慬慲慸慻慼慿憀憁憃憄憋憍憒憓憗憘憜憝憟憠憥憨憪憭憸憹憼懀懁懂懎懏懕懜懝懞懟懡懢懧懩懥\"],[\"8fbfa1\",\"懬懭懯戁戃戄戇戓戕戜戠戢戣戧戩戫戹戽扂扃扄扆扌扐扑扒扔扖扚扜扤扭扯扳扺扽抍抎抏抐抦抨抳抶抷抺抾抿拄拎拕拖拚拪拲拴拼拽挃挄挊挋挍挐挓挖挘挩挪挭挵挶挹挼捁捂捃捄捆捊捋捎捒捓捔捘捛捥捦捬捭捱捴捵\"],[\"8fc0a1\",\"捸捼捽捿掂掄掇掊掐掔掕掙掚掞掤掦掭掮掯掽揁揅揈揎揑揓揔揕揜揠揥揪揬揲揳揵揸揹搉搊搐搒搔搘搞搠搢搤搥搩搪搯搰搵搽搿摋摏摑摒摓摔摚摛摜摝摟摠摡摣摭摳摴摻摽撅撇撏撐撑撘撙撛撝撟撡撣撦撨撬撳撽撾撿\"],[\"8fc1a1\",\"擄擉擊擋擌擎擐擑擕擗擤擥擩擪擭擰擵擷擻擿攁攄攈攉攊攏攓攔攖攙攛攞攟攢攦攩攮攱攺攼攽敃敇敉敐敒敔敟敠敧敫敺敽斁斅斊斒斕斘斝斠斣斦斮斲斳斴斿旂旈旉旎旐旔旖旘旟旰旲旴旵旹旾旿昀昄昈昉昍昑昒昕昖昝\"],[\"8fc2a1\",\"昞昡昢昣昤昦昩昪昫昬昮昰昱昳昹昷晀晅晆晊晌晑晎晗晘晙晛晜晠晡曻晪晫晬晾晳晵晿晷晸晹晻暀晼暋暌暍暐暒暙暚暛暜暟暠暤暭暱暲暵暻暿曀曂曃曈曌曎曏曔曛曟曨曫曬曮曺朅朇朎朓朙朜朠朢朳朾杅杇杈杌杔杕杝\"],[\"8fc3a1\",\"杦杬杮杴杶杻极构枎枏枑枓枖枘枙枛枰枱枲枵枻枼枽柹柀柂柃柅柈柉柒柗柙柜柡柦柰柲柶柷桒栔栙栝栟栨栧栬栭栯栰栱栳栻栿桄桅桊桌桕桗桘桛桫桮\",4,\"桵桹桺桻桼梂梄梆梈梖梘梚梜梡梣梥梩梪梮梲梻棅棈棌棏\"],[\"8fc4a1\",\"棐棑棓棖棙棜棝棥棨棪棫棬棭棰棱棵棶棻棼棽椆椉椊椐椑椓椖椗椱椳椵椸椻楂楅楉楎楗楛楣楤楥楦楨楩楬楰楱楲楺楻楿榀榍榒榖榘榡榥榦榨榫榭榯榷榸榺榼槅槈槑槖槗槢槥槮槯槱槳槵槾樀樁樃樏樑樕樚樝樠樤樨樰樲\"],[\"8fc5a1\",\"樴樷樻樾樿橅橆橉橊橎橐橑橒橕橖橛橤橧橪橱橳橾檁檃檆檇檉檋檑檛檝檞檟檥檫檯檰檱檴檽檾檿櫆櫉櫈櫌櫐櫔櫕櫖櫜櫝櫤櫧櫬櫰櫱櫲櫼櫽欂欃欆欇欉欏欐欑欗欛欞欤欨欫欬欯欵欶欻欿歆歊歍歒歖歘歝歠歧歫歮歰歵歽\"],[\"8fc6a1\",\"歾殂殅殗殛殟殠殢殣殨殩殬殭殮殰殸殹殽殾毃毄毉毌毖毚毡毣毦毧毮毱毷毹毿氂氄氅氉氍氎氐氒氙氟氦氧氨氬氮氳氵氶氺氻氿汊汋汍汏汒汔汙汛汜汫汭汯汴汶汸汹汻沅沆沇沉沔沕沗沘沜沟沰沲沴泂泆泍泏泐泑泒泔泖\"],[\"8fc7a1\",\"泚泜泠泧泩泫泬泮泲泴洄洇洊洎洏洑洓洚洦洧洨汧洮洯洱洹洼洿浗浞浟浡浥浧浯浰浼涂涇涑涒涔涖涗涘涪涬涴涷涹涽涿淄淈淊淎淏淖淛淝淟淠淢淥淩淯淰淴淶淼渀渄渞渢渧渲渶渹渻渼湄湅湈湉湋湏湑湒湓湔湗湜湝湞\"],[\"8fc8a1\",\"湢湣湨湳湻湽溍溓溙溠溧溭溮溱溳溻溿滀滁滃滇滈滊滍滎滏滫滭滮滹滻滽漄漈漊漌漍漖漘漚漛漦漩漪漯漰漳漶漻漼漭潏潑潒潓潗潙潚潝潞潡潢潨潬潽潾澃澇澈澋澌澍澐澒澓澔澖澚澟澠澥澦澧澨澮澯澰澵澶澼濅濇濈濊\"],[\"8fc9a1\",\"濚濞濨濩濰濵濹濼濽瀀瀅瀆瀇瀍瀗瀠瀣瀯瀴瀷瀹瀼灃灄灈灉灊灋灔灕灝灞灎灤灥灬灮灵灶灾炁炅炆炔\",4,\"炛炤炫炰炱炴炷烊烑烓烔烕烖烘烜烤烺焃\",4,\"焋焌焏焞焠焫焭焯焰焱焸煁煅煆煇煊煋煐煒煗煚煜煞煠\"],[\"8fcaa1\",\"煨煹熀熅熇熌熒熚熛熠熢熯熰熲熳熺熿燀燁燄燋燌燓燖燙燚燜燸燾爀爇爈爉爓爗爚爝爟爤爫爯爴爸爹牁牂牃牅牎牏牐牓牕牖牚牜牞牠牣牨牫牮牯牱牷牸牻牼牿犄犉犍犎犓犛犨犭犮犱犴犾狁狇狉狌狕狖狘狟狥狳狴狺狻\"],[\"8fcba1\",\"狾猂猄猅猇猋猍猒猓猘猙猞猢猤猧猨猬猱猲猵猺猻猽獃獍獐獒獖獘獝獞獟獠獦獧獩獫獬獮獯獱獷獹獼玀玁玃玅玆玎玐玓玕玗玘玜玞玟玠玢玥玦玪玫玭玵玷玹玼玽玿珅珆珉珋珌珏珒珓珖珙珝珡珣珦珧珩珴珵珷珹珺珻珽\"],[\"8fcca1\",\"珿琀琁琄琇琊琑琚琛琤琦琨\",9,\"琹瑀瑃瑄瑆瑇瑋瑍瑑瑒瑗瑝瑢瑦瑧瑨瑫瑭瑮瑱瑲璀璁璅璆璇璉璏璐璑璒璘璙璚璜璟璠璡璣璦璨璩璪璫璮璯璱璲璵璹璻璿瓈瓉瓌瓐瓓瓘瓚瓛瓞瓟瓤瓨瓪瓫瓯瓴瓺瓻瓼瓿甆\"],[\"8fcda1\",\"甒甖甗甠甡甤甧甩甪甯甶甹甽甾甿畀畃畇畈畎畐畒畗畞畟畡畯畱畹\",5,\"疁疅疐疒疓疕疙疜疢疤疴疺疿痀痁痄痆痌痎痏痗痜痟痠痡痤痧痬痮痯痱痹瘀瘂瘃瘄瘇瘈瘊瘌瘏瘒瘓瘕瘖瘙瘛瘜瘝瘞瘣瘥瘦瘩瘭瘲瘳瘵瘸瘹\"],[\"8fcea1\",\"瘺瘼癊癀癁癃癄癅癉癋癕癙癟癤癥癭癮癯癱癴皁皅皌皍皕皛皜皝皟皠皢\",6,\"皪皭皽盁盅盉盋盌盎盔盙盠盦盨盬盰盱盶盹盼眀眆眊眎眒眔眕眗眙眚眜眢眨眭眮眯眴眵眶眹眽眾睂睅睆睊睍睎睏睒睖睗睜睞睟睠睢\"],[\"8fcfa1\",\"睤睧睪睬睰睲睳睴睺睽瞀瞄瞌瞍瞔瞕瞖瞚瞟瞢瞧瞪瞮瞯瞱瞵瞾矃矉矑矒矕矙矞矟矠矤矦矪矬矰矱矴矸矻砅砆砉砍砎砑砝砡砢砣砭砮砰砵砷硃硄硇硈硌硎硒硜硞硠硡硣硤硨硪确硺硾碊碏碔碘碡碝碞碟碤碨碬碭碰碱碲碳\"],[\"8fd0a1\",\"碻碽碿磇磈磉磌磎磒磓磕磖磤磛磟磠磡磦磪磲磳礀磶磷磺磻磿礆礌礐礚礜礞礟礠礥礧礩礭礱礴礵礻礽礿祄祅祆祊祋祏祑祔祘祛祜祧祩祫祲祹祻祼祾禋禌禑禓禔禕禖禘禛禜禡禨禩禫禯禱禴禸离秂秄秇秈秊秏秔秖秚秝秞\"],[\"8fd1a1\",\"秠秢秥秪秫秭秱秸秼稂稃稇稉稊稌稑稕稛稞稡稧稫稭稯稰稴稵稸稹稺穄穅穇穈穌穕穖穙穜穝穟穠穥穧穪穭穵穸穾窀窂窅窆窊窋窐窑窔窞窠窣窬窳窵窹窻窼竆竉竌竎竑竛竨竩竫竬竱竴竻竽竾笇笔笟笣笧笩笪笫笭笮笯笰\"],[\"8fd2a1\",\"笱笴笽笿筀筁筇筎筕筠筤筦筩筪筭筯筲筳筷箄箉箎箐箑箖箛箞箠箥箬箯箰箲箵箶箺箻箼箽篂篅篈篊篔篖篗篙篚篛篨篪篲篴篵篸篹篺篼篾簁簂簃簄簆簉簋簌簎簏簙簛簠簥簦簨簬簱簳簴簶簹簺籆籊籕籑籒籓籙\",5],[\"8fd3a1\",\"籡籣籧籩籭籮籰籲籹籼籽粆粇粏粔粞粠粦粰粶粷粺粻粼粿糄糇糈糉糍糏糓糔糕糗糙糚糝糦糩糫糵紃紇紈紉紏紑紒紓紖紝紞紣紦紪紭紱紼紽紾絀絁絇絈絍絑絓絗絙絚絜絝絥絧絪絰絸絺絻絿綁綂綃綅綆綈綋綌綍綑綖綗綝\"],[\"8fd4a1\",\"綞綦綧綪綳綶綷綹緂\",4,\"緌緍緎緗緙縀緢緥緦緪緫緭緱緵緶緹緺縈縐縑縕縗縜縝縠縧縨縬縭縯縳縶縿繄繅繇繎繐繒繘繟繡繢繥繫繮繯繳繸繾纁纆纇纊纍纑纕纘纚纝纞缼缻缽缾缿罃罄罇罏罒罓罛罜罝罡罣罤罥罦罭\"],[\"8fd5a1\",\"罱罽罾罿羀羋羍羏羐羑羖羗羜羡羢羦羪羭羴羼羿翀翃翈翎翏翛翟翣翥翨翬翮翯翲翺翽翾翿耇耈耊耍耎耏耑耓耔耖耝耞耟耠耤耦耬耮耰耴耵耷耹耺耼耾聀聄聠聤聦聭聱聵肁肈肎肜肞肦肧肫肸肹胈胍胏胒胔胕胗胘胠胭胮\"],[\"8fd6a1\",\"胰胲胳胶胹胺胾脃脋脖脗脘脜脞脠脤脧脬脰脵脺脼腅腇腊腌腒腗腠腡腧腨腩腭腯腷膁膐膄膅膆膋膎膖膘膛膞膢膮膲膴膻臋臃臅臊臎臏臕臗臛臝臞臡臤臫臬臰臱臲臵臶臸臹臽臿舀舃舏舓舔舙舚舝舡舢舨舲舴舺艃艄艅艆\"],[\"8fd7a1\",\"艋艎艏艑艖艜艠艣艧艭艴艻艽艿芀芁芃芄芇芉芊芎芑芔芖芘芚芛芠芡芣芤芧芨芩芪芮芰芲芴芷芺芼芾芿苆苐苕苚苠苢苤苨苪苭苯苶苷苽苾茀茁茇茈茊茋荔茛茝茞茟茡茢茬茭茮茰茳茷茺茼茽荂荃荄荇荍荎荑荕荖荗荰荸\"],[\"8fd8a1\",\"荽荿莀莂莄莆莍莒莔莕莘莙莛莜莝莦莧莩莬莾莿菀菇菉菏菐菑菔菝荓菨菪菶菸菹菼萁萆萊萏萑萕萙莭萯萹葅葇葈葊葍葏葑葒葖葘葙葚葜葠葤葥葧葪葰葳葴葶葸葼葽蒁蒅蒒蒓蒕蒞蒦蒨蒩蒪蒯蒱蒴蒺蒽蒾蓀蓂蓇蓈蓌蓏蓓\"],[\"8fd9a1\",\"蓜蓧蓪蓯蓰蓱蓲蓷蔲蓺蓻蓽蔂蔃蔇蔌蔎蔐蔜蔞蔢蔣蔤蔥蔧蔪蔫蔯蔳蔴蔶蔿蕆蕏\",4,\"蕖蕙蕜\",6,\"蕤蕫蕯蕹蕺蕻蕽蕿薁薅薆薉薋薌薏薓薘薝薟薠薢薥薧薴薶薷薸薼薽薾薿藂藇藊藋藎薭藘藚藟藠藦藨藭藳藶藼\"],[\"8fdaa1\",\"藿蘀蘄蘅蘍蘎蘐蘑蘒蘘蘙蘛蘞蘡蘧蘩蘶蘸蘺蘼蘽虀虂虆虒虓虖虗虘虙虝虠\",4,\"虩虬虯虵虶虷虺蚍蚑蚖蚘蚚蚜蚡蚦蚧蚨蚭蚱蚳蚴蚵蚷蚸蚹蚿蛀蛁蛃蛅蛑蛒蛕蛗蛚蛜蛠蛣蛥蛧蚈蛺蛼蛽蜄蜅蜇蜋蜎蜏蜐蜓蜔蜙蜞蜟蜡蜣\"],[\"8fdba1\",\"蜨蜮蜯蜱蜲蜹蜺蜼蜽蜾蝀蝃蝅蝍蝘蝝蝡蝤蝥蝯蝱蝲蝻螃\",6,\"螋螌螐螓螕螗螘螙螞螠螣螧螬螭螮螱螵螾螿蟁蟈蟉蟊蟎蟕蟖蟙蟚蟜蟟蟢蟣蟤蟪蟫蟭蟱蟳蟸蟺蟿蠁蠃蠆蠉蠊蠋蠐蠙蠒蠓蠔蠘蠚蠛蠜蠞蠟蠨蠭蠮蠰蠲蠵\"],[\"8fdca1\",\"蠺蠼衁衃衅衈衉衊衋衎衑衕衖衘衚衜衟衠衤衩衱衹衻袀袘袚袛袜袟袠袨袪袺袽袾裀裊\",4,\"裑裒裓裛裞裧裯裰裱裵裷褁褆褍褎褏褕褖褘褙褚褜褠褦褧褨褰褱褲褵褹褺褾襀襂襅襆襉襏襒襗襚襛襜襡襢襣襫襮襰襳襵襺\"],[\"8fdda1\",\"襻襼襽覉覍覐覔覕覛覜覟覠覥覰覴覵覶覷覼觔\",4,\"觥觩觫觭觱觳觶觹觽觿訄訅訇訏訑訒訔訕訞訠訢訤訦訫訬訯訵訷訽訾詀詃詅詇詉詍詎詓詖詗詘詜詝詡詥詧詵詶詷詹詺詻詾詿誀誃誆誋誏誐誒誖誗誙誟誧誩誮誯誳\"],[\"8fdea1\",\"誶誷誻誾諃諆諈諉諊諑諓諔諕諗諝諟諬諰諴諵諶諼諿謅謆謋謑謜謞謟謊謭謰謷謼譂\",4,\"譈譒譓譔譙譍譞譣譭譶譸譹譼譾讁讄讅讋讍讏讔讕讜讞讟谸谹谽谾豅豇豉豋豏豑豓豔豗豘豛豝豙豣豤豦豨豩豭豳豵豶豻豾貆\"],[\"8fdfa1\",\"貇貋貐貒貓貙貛貜貤貹貺賅賆賉賋賏賖賕賙賝賡賨賬賯賰賲賵賷賸賾賿贁贃贉贒贗贛赥赩赬赮赿趂趄趈趍趐趑趕趞趟趠趦趫趬趯趲趵趷趹趻跀跅跆跇跈跊跎跑跔跕跗跙跤跥跧跬跰趼跱跲跴跽踁踄踅踆踋踑踔踖踠踡踢\"],[\"8fe0a1\",\"踣踦踧踱踳踶踷踸踹踽蹀蹁蹋蹍蹎蹏蹔蹛蹜蹝蹞蹡蹢蹩蹬蹭蹯蹰蹱蹹蹺蹻躂躃躉躐躒躕躚躛躝躞躢躧躩躭躮躳躵躺躻軀軁軃軄軇軏軑軔軜軨軮軰軱軷軹軺軭輀輂輇輈輏輐輖輗輘輞輠輡輣輥輧輨輬輭輮輴輵輶輷輺轀轁\"],[\"8fe1a1\",\"轃轇轏轑\",4,\"轘轝轞轥辝辠辡辤辥辦辵辶辸达迀迁迆迊迋迍运迒迓迕迠迣迤迨迮迱迵迶迻迾适逄逈逌逘逛逨逩逯逪逬逭逳逴逷逿遃遄遌遛遝遢遦遧遬遰遴遹邅邈邋邌邎邐邕邗邘邙邛邠邡邢邥邰邲邳邴邶邽郌邾郃\"],[\"8fe2a1\",\"郄郅郇郈郕郗郘郙郜郝郟郥郒郶郫郯郰郴郾郿鄀鄄鄅鄆鄈鄍鄐鄔鄖鄗鄘鄚鄜鄞鄠鄥鄢鄣鄧鄩鄮鄯鄱鄴鄶鄷鄹鄺鄼鄽酃酇酈酏酓酗酙酚酛酡酤酧酭酴酹酺酻醁醃醅醆醊醎醑醓醔醕醘醞醡醦醨醬醭醮醰醱醲醳醶醻醼醽醿\"],[\"8fe3a1\",\"釂釃釅釓釔釗釙釚釞釤釥釩釪釬\",5,\"釷釹釻釽鈀鈁鈄鈅鈆鈇鈉鈊鈌鈐鈒鈓鈖鈘鈜鈝鈣鈤鈥鈦鈨鈮鈯鈰鈳鈵鈶鈸鈹鈺鈼鈾鉀鉂鉃鉆鉇鉊鉍鉎鉏鉑鉘鉙鉜鉝鉠鉡鉥鉧鉨鉩鉮鉯鉰鉵\",4,\"鉻鉼鉽鉿銈銉銊銍銎銒銗\"],[\"8fe4a1\",\"銙銟銠銤銥銧銨銫銯銲銶銸銺銻銼銽銿\",4,\"鋅鋆鋇鋈鋋鋌鋍鋎鋐鋓鋕鋗鋘鋙鋜鋝鋟鋠鋡鋣鋥鋧鋨鋬鋮鋰鋹鋻鋿錀錂錈錍錑錔錕錜錝錞錟錡錤錥錧錩錪錳錴錶錷鍇鍈鍉鍐鍑鍒鍕鍗鍘鍚鍞鍤鍥鍧鍩鍪鍭鍯鍰鍱鍳鍴鍶\"],[\"8fe5a1\",\"鍺鍽鍿鎀鎁鎂鎈鎊鎋鎍鎏鎒鎕鎘鎛鎞鎡鎣鎤鎦鎨鎫鎴鎵鎶鎺鎩鏁鏄鏅鏆鏇鏉\",4,\"鏓鏙鏜鏞鏟鏢鏦鏧鏹鏷鏸鏺鏻鏽鐁鐂鐄鐈鐉鐍鐎鐏鐕鐖鐗鐟鐮鐯鐱鐲鐳鐴鐻鐿鐽鑃鑅鑈鑊鑌鑕鑙鑜鑟鑡鑣鑨鑫鑭鑮鑯鑱鑲钄钃镸镹\"],[\"8fe6a1\",\"镾閄閈閌閍閎閝閞閟閡閦閩閫閬閴閶閺閽閿闆闈闉闋闐闑闒闓闙闚闝闞闟闠闤闦阝阞阢阤阥阦阬阱阳阷阸阹阺阼阽陁陒陔陖陗陘陡陮陴陻陼陾陿隁隂隃隄隉隑隖隚隝隟隤隥隦隩隮隯隳隺雊雒嶲雘雚雝雞雟雩雯雱雺霂\"],[\"8fe7a1\",\"霃霅霉霚霛霝霡霢霣霨霱霳靁靃靊靎靏靕靗靘靚靛靣靧靪靮靳靶靷靸靻靽靿鞀鞉鞕鞖鞗鞙鞚鞞鞟鞢鞬鞮鞱鞲鞵鞶鞸鞹鞺鞼鞾鞿韁韄韅韇韉韊韌韍韎韐韑韔韗韘韙韝韞韠韛韡韤韯韱韴韷韸韺頇頊頙頍頎頔頖頜頞頠頣頦\"],[\"8fe8a1\",\"頫頮頯頰頲頳頵頥頾顄顇顊顑顒顓顖顗顙顚顢顣顥顦顪顬颫颭颮颰颴颷颸颺颻颿飂飅飈飌飡飣飥飦飧飪飳飶餂餇餈餑餕餖餗餚餛餜餟餢餦餧餫餱\",4,\"餹餺餻餼饀饁饆饇饈饍饎饔饘饙饛饜饞饟饠馛馝馟馦馰馱馲馵\"],[\"8fe9a1\",\"馹馺馽馿駃駉駓駔駙駚駜駞駧駪駫駬駰駴駵駹駽駾騂騃騄騋騌騐騑騖騞騠騢騣騤騧騭騮騳騵騶騸驇驁驄驊驋驌驎驑驔驖驝骪骬骮骯骲骴骵骶骹骻骾骿髁髃髆髈髎髐髒髕髖髗髛髜髠髤髥髧髩髬髲髳髵髹髺髽髿\",4],[\"8feaa1\",\"鬄鬅鬈鬉鬋鬌鬍鬎鬐鬒鬖鬙鬛鬜鬠鬦鬫鬭鬳鬴鬵鬷鬹鬺鬽魈魋魌魕魖魗魛魞魡魣魥魦魨魪\",4,\"魳魵魷魸魹魿鮀鮄鮅鮆鮇鮉鮊鮋鮍鮏鮐鮔鮚鮝鮞鮦鮧鮩鮬鮰鮱鮲鮷鮸鮻鮼鮾鮿鯁鯇鯈鯎鯐鯗鯘鯝鯟鯥鯧鯪鯫鯯鯳鯷鯸\"],[\"8feba1\",\"鯹鯺鯽鯿鰀鰂鰋鰏鰑鰖鰘鰙鰚鰜鰞鰢鰣鰦\",4,\"鰱鰵鰶鰷鰽鱁鱃鱄鱅鱉鱊鱎鱏鱐鱓鱔鱖鱘鱛鱝鱞鱟鱣鱩鱪鱜鱫鱨鱮鱰鱲鱵鱷鱻鳦鳲鳷鳹鴋鴂鴑鴗鴘鴜鴝鴞鴯鴰鴲鴳鴴鴺鴼鵅鴽鵂鵃鵇鵊鵓鵔鵟鵣鵢鵥鵩鵪鵫鵰鵶鵷鵻\"],[\"8feca1\",\"鵼鵾鶃鶄鶆鶊鶍鶎鶒鶓鶕鶖鶗鶘鶡鶪鶬鶮鶱鶵鶹鶼鶿鷃鷇鷉鷊鷔鷕鷖鷗鷚鷞鷟鷠鷥鷧鷩鷫鷮鷰鷳鷴鷾鸊鸂鸇鸎鸐鸑鸒鸕鸖鸙鸜鸝鹺鹻鹼麀麂麃麄麅麇麎麏麖麘麛麞麤麨麬麮麯麰麳麴麵黆黈黋黕黟黤黧黬黭黮黰黱黲黵\"],[\"8feda1\",\"黸黿鼂鼃鼉鼏鼐鼑鼒鼔鼖鼗鼙鼚鼛鼟鼢鼦鼪鼫鼯鼱鼲鼴鼷鼹鼺鼼鼽鼿齁齃\",4,\"齓齕齖齗齘齚齝齞齨齩齭\",4,\"齳齵齺齽龏龐龑龒龔龖龗龞龡龢龣龥\"]]");

/***/ }),
/* 402 */
/***/ (function(module) {

module.exports = JSON.parse("{\"uChars\":[128,165,169,178,184,216,226,235,238,244,248,251,253,258,276,284,300,325,329,334,364,463,465,467,469,471,473,475,477,506,594,610,712,716,730,930,938,962,970,1026,1104,1106,8209,8215,8218,8222,8231,8241,8244,8246,8252,8365,8452,8454,8458,8471,8482,8556,8570,8596,8602,8713,8720,8722,8726,8731,8737,8740,8742,8748,8751,8760,8766,8777,8781,8787,8802,8808,8816,8854,8858,8870,8896,8979,9322,9372,9548,9588,9616,9622,9634,9652,9662,9672,9676,9680,9702,9735,9738,9793,9795,11906,11909,11913,11917,11928,11944,11947,11951,11956,11960,11964,11979,12284,12292,12312,12319,12330,12351,12436,12447,12535,12543,12586,12842,12850,12964,13200,13215,13218,13253,13263,13267,13270,13384,13428,13727,13839,13851,14617,14703,14801,14816,14964,15183,15471,15585,16471,16736,17208,17325,17330,17374,17623,17997,18018,18212,18218,18301,18318,18760,18811,18814,18820,18823,18844,18848,18872,19576,19620,19738,19887,40870,59244,59336,59367,59413,59417,59423,59431,59437,59443,59452,59460,59478,59493,63789,63866,63894,63976,63986,64016,64018,64021,64025,64034,64037,64042,65074,65093,65107,65112,65127,65132,65375,65510,65536],\"gbChars\":[0,36,38,45,50,81,89,95,96,100,103,104,105,109,126,133,148,172,175,179,208,306,307,308,309,310,311,312,313,341,428,443,544,545,558,741,742,749,750,805,819,820,7922,7924,7925,7927,7934,7943,7944,7945,7950,8062,8148,8149,8152,8164,8174,8236,8240,8262,8264,8374,8380,8381,8384,8388,8390,8392,8393,8394,8396,8401,8406,8416,8419,8424,8437,8439,8445,8482,8485,8496,8521,8603,8936,8946,9046,9050,9063,9066,9076,9092,9100,9108,9111,9113,9131,9162,9164,9218,9219,11329,11331,11334,11336,11346,11361,11363,11366,11370,11372,11375,11389,11682,11686,11687,11692,11694,11714,11716,11723,11725,11730,11736,11982,11989,12102,12336,12348,12350,12384,12393,12395,12397,12510,12553,12851,12962,12973,13738,13823,13919,13933,14080,14298,14585,14698,15583,15847,16318,16434,16438,16481,16729,17102,17122,17315,17320,17402,17418,17859,17909,17911,17915,17916,17936,17939,17961,18664,18703,18814,18962,19043,33469,33470,33471,33484,33485,33490,33497,33501,33505,33513,33520,33536,33550,37845,37921,37948,38029,38038,38064,38065,38066,38069,38075,38076,38078,39108,39109,39113,39114,39115,39116,39265,39394,189000]}");

/***/ }),
/* 403 */
/***/ (function(module) {

module.exports = JSON.parse("[[\"0\",\"\\u0000\",127],[\"8141\",\"갂갃갅갆갋\",4,\"갘갞갟갡갢갣갥\",6,\"갮갲갳갴\"],[\"8161\",\"갵갶갷갺갻갽갾갿걁\",9,\"걌걎\",5,\"걕\"],[\"8181\",\"걖걗걙걚걛걝\",18,\"걲걳걵걶걹걻\",4,\"겂겇겈겍겎겏겑겒겓겕\",6,\"겞겢\",5,\"겫겭겮겱\",6,\"겺겾겿곀곂곃곅곆곇곉곊곋곍\",7,\"곖곘\",7,\"곢곣곥곦곩곫곭곮곲곴곷\",4,\"곾곿괁괂괃괅괇\",4,\"괎괐괒괓\"],[\"8241\",\"괔괕괖괗괙괚괛괝괞괟괡\",7,\"괪괫괮\",5],[\"8261\",\"괶괷괹괺괻괽\",6,\"굆굈굊\",5,\"굑굒굓굕굖굗\"],[\"8281\",\"굙\",7,\"굢굤\",7,\"굮굯굱굲굷굸굹굺굾궀궃\",4,\"궊궋궍궎궏궑\",10,\"궞\",5,\"궥\",17,\"궸\",7,\"귂귃귅귆귇귉\",6,\"귒귔\",7,\"귝귞귟귡귢귣귥\",18],[\"8341\",\"귺귻귽귾긂\",5,\"긊긌긎\",5,\"긕\",7],[\"8361\",\"긝\",18,\"긲긳긵긶긹긻긼\"],[\"8381\",\"긽긾긿깂깄깇깈깉깋깏깑깒깓깕깗\",4,\"깞깢깣깤깦깧깪깫깭깮깯깱\",6,\"깺깾\",5,\"꺆\",5,\"꺍\",46,\"꺿껁껂껃껅\",6,\"껎껒\",5,\"껚껛껝\",8],[\"8441\",\"껦껧껩껪껬껮\",5,\"껵껶껷껹껺껻껽\",8],[\"8461\",\"꼆꼉꼊꼋꼌꼎꼏꼑\",18],[\"8481\",\"꼤\",7,\"꼮꼯꼱꼳꼵\",6,\"꼾꽀꽄꽅꽆꽇꽊\",5,\"꽑\",10,\"꽞\",5,\"꽦\",18,\"꽺\",5,\"꾁꾂꾃꾅꾆꾇꾉\",6,\"꾒꾓꾔꾖\",5,\"꾝\",26,\"꾺꾻꾽꾾\"],[\"8541\",\"꾿꿁\",5,\"꿊꿌꿏\",4,\"꿕\",6,\"꿝\",4],[\"8561\",\"꿢\",5,\"꿪\",5,\"꿲꿳꿵꿶꿷꿹\",6,\"뀂뀃\"],[\"8581\",\"뀅\",6,\"뀍뀎뀏뀑뀒뀓뀕\",6,\"뀞\",9,\"뀩\",26,\"끆끇끉끋끍끏끐끑끒끖끘끚끛끜끞\",29,\"끾끿낁낂낃낅\",6,\"낎낐낒\",5,\"낛낝낞낣낤\"],[\"8641\",\"낥낦낧낪낰낲낶낷낹낺낻낽\",6,\"냆냊\",5,\"냒\"],[\"8661\",\"냓냕냖냗냙\",6,\"냡냢냣냤냦\",10],[\"8681\",\"냱\",22,\"넊넍넎넏넑넔넕넖넗넚넞\",4,\"넦넧넩넪넫넭\",6,\"넶넺\",5,\"녂녃녅녆녇녉\",6,\"녒녓녖녗녙녚녛녝녞녟녡\",22,\"녺녻녽녾녿놁놃\",4,\"놊놌놎놏놐놑놕놖놗놙놚놛놝\"],[\"8741\",\"놞\",9,\"놩\",15],[\"8761\",\"놹\",18,\"뇍뇎뇏뇑뇒뇓뇕\"],[\"8781\",\"뇖\",5,\"뇞뇠\",7,\"뇪뇫뇭뇮뇯뇱\",7,\"뇺뇼뇾\",5,\"눆눇눉눊눍\",6,\"눖눘눚\",5,\"눡\",18,\"눵\",6,\"눽\",26,\"뉙뉚뉛뉝뉞뉟뉡\",6,\"뉪\",4],[\"8841\",\"뉯\",4,\"뉶\",5,\"뉽\",6,\"늆늇늈늊\",4],[\"8861\",\"늏늒늓늕늖늗늛\",4,\"늢늤늧늨늩늫늭늮늯늱늲늳늵늶늷\"],[\"8881\",\"늸\",15,\"닊닋닍닎닏닑닓\",4,\"닚닜닞닟닠닡닣닧닩닪닰닱닲닶닼닽닾댂댃댅댆댇댉\",6,\"댒댖\",5,\"댝\",54,\"덗덙덚덝덠덡덢덣\"],[\"8941\",\"덦덨덪덬덭덯덲덳덵덶덷덹\",6,\"뎂뎆\",5,\"뎍\"],[\"8961\",\"뎎뎏뎑뎒뎓뎕\",10,\"뎢\",5,\"뎩뎪뎫뎭\"],[\"8981\",\"뎮\",21,\"돆돇돉돊돍돏돑돒돓돖돘돚돜돞돟돡돢돣돥돦돧돩\",18,\"돽\",18,\"됑\",6,\"됙됚됛됝됞됟됡\",6,\"됪됬\",7,\"됵\",15],[\"8a41\",\"둅\",10,\"둒둓둕둖둗둙\",6,\"둢둤둦\"],[\"8a61\",\"둧\",4,\"둭\",18,\"뒁뒂\"],[\"8a81\",\"뒃\",4,\"뒉\",19,\"뒞\",5,\"뒥뒦뒧뒩뒪뒫뒭\",7,\"뒶뒸뒺\",5,\"듁듂듃듅듆듇듉\",6,\"듑듒듓듔듖\",5,\"듞듟듡듢듥듧\",4,\"듮듰듲\",5,\"듹\",26,\"딖딗딙딚딝\"],[\"8b41\",\"딞\",5,\"딦딫\",4,\"딲딳딵딶딷딹\",6,\"땂땆\"],[\"8b61\",\"땇땈땉땊땎땏땑땒땓땕\",6,\"땞땢\",8],[\"8b81\",\"땫\",52,\"떢떣떥떦떧떩떬떭떮떯떲떶\",4,\"떾떿뗁뗂뗃뗅\",6,\"뗎뗒\",5,\"뗙\",18,\"뗭\",18],[\"8c41\",\"똀\",15,\"똒똓똕똖똗똙\",4],[\"8c61\",\"똞\",6,\"똦\",5,\"똭\",6,\"똵\",5],[\"8c81\",\"똻\",12,\"뙉\",26,\"뙥뙦뙧뙩\",50,\"뚞뚟뚡뚢뚣뚥\",5,\"뚭뚮뚯뚰뚲\",16],[\"8d41\",\"뛃\",16,\"뛕\",8],[\"8d61\",\"뛞\",17,\"뛱뛲뛳뛵뛶뛷뛹뛺\"],[\"8d81\",\"뛻\",4,\"뜂뜃뜄뜆\",33,\"뜪뜫뜭뜮뜱\",6,\"뜺뜼\",7,\"띅띆띇띉띊띋띍\",6,\"띖\",9,\"띡띢띣띥띦띧띩\",6,\"띲띴띶\",5,\"띾띿랁랂랃랅\",6,\"랎랓랔랕랚랛랝랞\"],[\"8e41\",\"랟랡\",6,\"랪랮\",5,\"랶랷랹\",8],[\"8e61\",\"럂\",4,\"럈럊\",19],[\"8e81\",\"럞\",13,\"럮럯럱럲럳럵\",6,\"럾렂\",4,\"렊렋렍렎렏렑\",6,\"렚렜렞\",5,\"렦렧렩렪렫렭\",6,\"렶렺\",5,\"롁롂롃롅\",11,\"롒롔\",7,\"롞롟롡롢롣롥\",6,\"롮롰롲\",5,\"롹롺롻롽\",7],[\"8f41\",\"뢅\",7,\"뢎\",17],[\"8f61\",\"뢠\",7,\"뢩\",6,\"뢱뢲뢳뢵뢶뢷뢹\",4],[\"8f81\",\"뢾뢿룂룄룆\",5,\"룍룎룏룑룒룓룕\",7,\"룞룠룢\",5,\"룪룫룭룮룯룱\",6,\"룺룼룾\",5,\"뤅\",18,\"뤙\",6,\"뤡\",26,\"뤾뤿륁륂륃륅\",6,\"륍륎륐륒\",5],[\"9041\",\"륚륛륝륞륟륡\",6,\"륪륬륮\",5,\"륶륷륹륺륻륽\"],[\"9061\",\"륾\",5,\"릆릈릋릌릏\",15],[\"9081\",\"릟\",12,\"릮릯릱릲릳릵\",6,\"릾맀맂\",5,\"맊맋맍맓\",4,\"맚맜맟맠맢맦맧맩맪맫맭\",6,\"맶맻\",4,\"먂\",5,\"먉\",11,\"먖\",33,\"먺먻먽먾먿멁멃멄멅멆\"],[\"9141\",\"멇멊멌멏멐멑멒멖멗멙멚멛멝\",6,\"멦멪\",5],[\"9161\",\"멲멳멵멶멷멹\",9,\"몆몈몉몊몋몍\",5],[\"9181\",\"몓\",20,\"몪몭몮몯몱몳\",4,\"몺몼몾\",5,\"뫅뫆뫇뫉\",14,\"뫚\",33,\"뫽뫾뫿묁묂묃묅\",7,\"묎묐묒\",5,\"묙묚묛묝묞묟묡\",6],[\"9241\",\"묨묪묬\",7,\"묷묹묺묿\",4,\"뭆뭈뭊뭋뭌뭎뭑뭒\"],[\"9261\",\"뭓뭕뭖뭗뭙\",7,\"뭢뭤\",7,\"뭭\",4],[\"9281\",\"뭲\",21,\"뮉뮊뮋뮍뮎뮏뮑\",18,\"뮥뮦뮧뮩뮪뮫뮭\",6,\"뮵뮶뮸\",7,\"믁믂믃믅믆믇믉\",6,\"믑믒믔\",35,\"믺믻믽믾밁\"],[\"9341\",\"밃\",4,\"밊밎밐밒밓밙밚밠밡밢밣밦밨밪밫밬밮밯밲밳밵\"],[\"9361\",\"밶밷밹\",6,\"뱂뱆뱇뱈뱊뱋뱎뱏뱑\",8],[\"9381\",\"뱚뱛뱜뱞\",37,\"벆벇벉벊벍벏\",4,\"벖벘벛\",4,\"벢벣벥벦벩\",6,\"벲벶\",5,\"벾벿볁볂볃볅\",7,\"볎볒볓볔볖볗볙볚볛볝\",22,\"볷볹볺볻볽\"],[\"9441\",\"볾\",5,\"봆봈봊\",5,\"봑봒봓봕\",8],[\"9461\",\"봞\",5,\"봥\",6,\"봭\",12],[\"9481\",\"봺\",5,\"뵁\",6,\"뵊뵋뵍뵎뵏뵑\",6,\"뵚\",9,\"뵥뵦뵧뵩\",22,\"붂붃붅붆붋\",4,\"붒붔붖붗붘붛붝\",6,\"붥\",10,\"붱\",6,\"붹\",24],[\"9541\",\"뷒뷓뷖뷗뷙뷚뷛뷝\",11,\"뷪\",5,\"뷱\"],[\"9561\",\"뷲뷳뷵뷶뷷뷹\",6,\"븁븂븄븆\",5,\"븎븏븑븒븓\"],[\"9581\",\"븕\",6,\"븞븠\",35,\"빆빇빉빊빋빍빏\",4,\"빖빘빜빝빞빟빢빣빥빦빧빩빫\",4,\"빲빶\",4,\"빾빿뺁뺂뺃뺅\",6,\"뺎뺒\",5,\"뺚\",13,\"뺩\",14],[\"9641\",\"뺸\",23,\"뻒뻓\"],[\"9661\",\"뻕뻖뻙\",6,\"뻡뻢뻦\",5,\"뻭\",8],[\"9681\",\"뻶\",10,\"뼂\",5,\"뼊\",13,\"뼚뼞\",33,\"뽂뽃뽅뽆뽇뽉\",6,\"뽒뽓뽔뽖\",44],[\"9741\",\"뾃\",16,\"뾕\",8],[\"9761\",\"뾞\",17,\"뾱\",7],[\"9781\",\"뾹\",11,\"뿆\",5,\"뿎뿏뿑뿒뿓뿕\",6,\"뿝뿞뿠뿢\",89,\"쀽쀾쀿\"],[\"9841\",\"쁀\",16,\"쁒\",5,\"쁙쁚쁛\"],[\"9861\",\"쁝쁞쁟쁡\",6,\"쁪\",15],[\"9881\",\"쁺\",21,\"삒삓삕삖삗삙\",6,\"삢삤삦\",5,\"삮삱삲삷\",4,\"삾샂샃샄샆샇샊샋샍샎샏샑\",6,\"샚샞\",5,\"샦샧샩샪샫샭\",6,\"샶샸샺\",5,\"섁섂섃섅섆섇섉\",6,\"섑섒섓섔섖\",5,\"섡섢섥섨섩섪섫섮\"],[\"9941\",\"섲섳섴섵섷섺섻섽섾섿셁\",6,\"셊셎\",5,\"셖셗\"],[\"9961\",\"셙셚셛셝\",6,\"셦셪\",5,\"셱셲셳셵셶셷셹셺셻\"],[\"9981\",\"셼\",8,\"솆\",5,\"솏솑솒솓솕솗\",4,\"솞솠솢솣솤솦솧솪솫솭솮솯솱\",11,\"솾\",5,\"쇅쇆쇇쇉쇊쇋쇍\",6,\"쇕쇖쇙\",6,\"쇡쇢쇣쇥쇦쇧쇩\",6,\"쇲쇴\",7,\"쇾쇿숁숂숃숅\",6,\"숎숐숒\",5,\"숚숛숝숞숡숢숣\"],[\"9a41\",\"숤숥숦숧숪숬숮숰숳숵\",16],[\"9a61\",\"쉆쉇쉉\",6,\"쉒쉓쉕쉖쉗쉙\",6,\"쉡쉢쉣쉤쉦\"],[\"9a81\",\"쉧\",4,\"쉮쉯쉱쉲쉳쉵\",6,\"쉾슀슂\",5,\"슊\",5,\"슑\",6,\"슙슚슜슞\",5,\"슦슧슩슪슫슮\",5,\"슶슸슺\",33,\"싞싟싡싢싥\",5,\"싮싰싲싳싴싵싷싺싽싾싿쌁\",6,\"쌊쌋쌎쌏\"],[\"9b41\",\"쌐쌑쌒쌖쌗쌙쌚쌛쌝\",6,\"쌦쌧쌪\",8],[\"9b61\",\"쌳\",17,\"썆\",7],[\"9b81\",\"썎\",25,\"썪썫썭썮썯썱썳\",4,\"썺썻썾\",5,\"쎅쎆쎇쎉쎊쎋쎍\",50,\"쏁\",22,\"쏚\"],[\"9c41\",\"쏛쏝쏞쏡쏣\",4,\"쏪쏫쏬쏮\",5,\"쏶쏷쏹\",5],[\"9c61\",\"쏿\",8,\"쐉\",6,\"쐑\",9],[\"9c81\",\"쐛\",8,\"쐥\",6,\"쐭쐮쐯쐱쐲쐳쐵\",6,\"쐾\",9,\"쑉\",26,\"쑦쑧쑩쑪쑫쑭\",6,\"쑶쑷쑸쑺\",5,\"쒁\",18,\"쒕\",6,\"쒝\",12],[\"9d41\",\"쒪\",13,\"쒹쒺쒻쒽\",8],[\"9d61\",\"쓆\",25],[\"9d81\",\"쓠\",8,\"쓪\",5,\"쓲쓳쓵쓶쓷쓹쓻쓼쓽쓾씂\",9,\"씍씎씏씑씒씓씕\",6,\"씝\",10,\"씪씫씭씮씯씱\",6,\"씺씼씾\",5,\"앆앇앋앏앐앑앒앖앚앛앜앟앢앣앥앦앧앩\",6,\"앲앶\",5,\"앾앿얁얂얃얅얆얈얉얊얋얎얐얒얓얔\"],[\"9e41\",\"얖얙얚얛얝얞얟얡\",7,\"얪\",9,\"얶\"],[\"9e61\",\"얷얺얿\",4,\"엋엍엏엒엓엕엖엗엙\",6,\"엢엤엦엧\"],[\"9e81\",\"엨엩엪엫엯엱엲엳엵엸엹엺엻옂옃옄옉옊옋옍옎옏옑\",6,\"옚옝\",6,\"옦옧옩옪옫옯옱옲옶옸옺옼옽옾옿왂왃왅왆왇왉\",6,\"왒왖\",5,\"왞왟왡\",10,\"왭왮왰왲\",5,\"왺왻왽왾왿욁\",6,\"욊욌욎\",5,\"욖욗욙욚욛욝\",6,\"욦\"],[\"9f41\",\"욨욪\",5,\"욲욳욵욶욷욻\",4,\"웂웄웆\",5,\"웎\"],[\"9f61\",\"웏웑웒웓웕\",6,\"웞웟웢\",5,\"웪웫웭웮웯웱웲\"],[\"9f81\",\"웳\",4,\"웺웻웼웾\",5,\"윆윇윉윊윋윍\",6,\"윖윘윚\",5,\"윢윣윥윦윧윩\",6,\"윲윴윶윸윹윺윻윾윿읁읂읃읅\",4,\"읋읎읐읙읚읛읝읞읟읡\",6,\"읩읪읬\",7,\"읶읷읹읺읻읿잀잁잂잆잋잌잍잏잒잓잕잙잛\",4,\"잢잧\",4,\"잮잯잱잲잳잵잶잷\"],[\"a041\",\"잸잹잺잻잾쟂\",5,\"쟊쟋쟍쟏쟑\",6,\"쟙쟚쟛쟜\"],[\"a061\",\"쟞\",5,\"쟥쟦쟧쟩쟪쟫쟭\",13],[\"a081\",\"쟻\",4,\"젂젃젅젆젇젉젋\",4,\"젒젔젗\",4,\"젞젟젡젢젣젥\",6,\"젮젰젲\",5,\"젹젺젻젽젾젿졁\",6,\"졊졋졎\",5,\"졕\",26,\"졲졳졵졶졷졹졻\",4,\"좂좄좈좉좊좎\",5,\"좕\",7,\"좞좠좢좣좤\"],[\"a141\",\"좥좦좧좩\",18,\"좾좿죀죁\"],[\"a161\",\"죂죃죅죆죇죉죊죋죍\",6,\"죖죘죚\",5,\"죢죣죥\"],[\"a181\",\"죦\",14,\"죶\",5,\"죾죿줁줂줃줇\",4,\"줎　、。·‥…¨〃­―∥＼∼‘’“”〔〕〈\",9,\"±×÷≠≤≥∞∴°′″℃Å￠￡￥♂♀∠⊥⌒∂∇≡≒§※☆★○●◎◇◆□■△▲▽▼→←↑↓↔〓≪≫√∽∝∵∫∬∈∋⊆⊇⊂⊃∪∩∧∨￢\"],[\"a241\",\"줐줒\",5,\"줙\",18],[\"a261\",\"줭\",6,\"줵\",18],[\"a281\",\"쥈\",7,\"쥒쥓쥕쥖쥗쥙\",6,\"쥢쥤\",7,\"쥭쥮쥯⇒⇔∀∃´～ˇ˘˝˚˙¸˛¡¿ː∮∑∏¤℉‰◁◀▷▶♤♠♡♥♧♣⊙◈▣◐◑▒▤▥▨▧▦▩♨☏☎☜☞¶†‡↕↗↙↖↘♭♩♪♬㉿㈜№㏇™㏂㏘℡€®\"],[\"a341\",\"쥱쥲쥳쥵\",6,\"쥽\",10,\"즊즋즍즎즏\"],[\"a361\",\"즑\",6,\"즚즜즞\",16],[\"a381\",\"즯\",16,\"짂짃짅짆짉짋\",4,\"짒짔짗짘짛！\",58,\"￦］\",32,\"￣\"],[\"a441\",\"짞짟짡짣짥짦짨짩짪짫짮짲\",5,\"짺짻짽짾짿쨁쨂쨃쨄\"],[\"a461\",\"쨅쨆쨇쨊쨎\",5,\"쨕쨖쨗쨙\",12],[\"a481\",\"쨦쨧쨨쨪\",28,\"ㄱ\",93],[\"a541\",\"쩇\",4,\"쩎쩏쩑쩒쩓쩕\",6,\"쩞쩢\",5,\"쩩쩪\"],[\"a561\",\"쩫\",17,\"쩾\",5,\"쪅쪆\"],[\"a581\",\"쪇\",16,\"쪙\",14,\"ⅰ\",9],[\"a5b0\",\"Ⅰ\",9],[\"a5c1\",\"Α\",16,\"Σ\",6],[\"a5e1\",\"α\",16,\"σ\",6],[\"a641\",\"쪨\",19,\"쪾쪿쫁쫂쫃쫅\"],[\"a661\",\"쫆\",5,\"쫎쫐쫒쫔쫕쫖쫗쫚\",5,\"쫡\",6],[\"a681\",\"쫨쫩쫪쫫쫭\",6,\"쫵\",18,\"쬉쬊─│┌┐┘└├┬┤┴┼━┃┏┓┛┗┣┳┫┻╋┠┯┨┷┿┝┰┥┸╂┒┑┚┙┖┕┎┍┞┟┡┢┦┧┩┪┭┮┱┲┵┶┹┺┽┾╀╁╃\",7],[\"a741\",\"쬋\",4,\"쬑쬒쬓쬕쬖쬗쬙\",6,\"쬢\",7],[\"a761\",\"쬪\",22,\"쭂쭃쭄\"],[\"a781\",\"쭅쭆쭇쭊쭋쭍쭎쭏쭑\",6,\"쭚쭛쭜쭞\",5,\"쭥\",7,\"㎕㎖㎗ℓ㎘㏄㎣㎤㎥㎦㎙\",9,\"㏊㎍㎎㎏㏏㎈㎉㏈㎧㎨㎰\",9,\"㎀\",4,\"㎺\",5,\"㎐\",4,\"Ω㏀㏁㎊㎋㎌㏖㏅㎭㎮㎯㏛㎩㎪㎫㎬㏝㏐㏓㏃㏉㏜㏆\"],[\"a841\",\"쭭\",10,\"쭺\",14],[\"a861\",\"쮉\",18,\"쮝\",6],[\"a881\",\"쮤\",19,\"쮹\",11,\"ÆÐªĦ\"],[\"a8a6\",\"Ĳ\"],[\"a8a8\",\"ĿŁØŒºÞŦŊ\"],[\"a8b1\",\"㉠\",27,\"ⓐ\",25,\"①\",14,\"½⅓⅔¼¾⅛⅜⅝⅞\"],[\"a941\",\"쯅\",14,\"쯕\",10],[\"a961\",\"쯠쯡쯢쯣쯥쯦쯨쯪\",18],[\"a981\",\"쯽\",14,\"찎찏찑찒찓찕\",6,\"찞찟찠찣찤æđðħıĳĸŀłøœßþŧŋŉ㈀\",27,\"⒜\",25,\"⑴\",14,\"¹²³⁴ⁿ₁₂₃₄\"],[\"aa41\",\"찥찦찪찫찭찯찱\",6,\"찺찿\",4,\"챆챇챉챊챋챍챎\"],[\"aa61\",\"챏\",4,\"챖챚\",5,\"챡챢챣챥챧챩\",6,\"챱챲\"],[\"aa81\",\"챳챴챶\",29,\"ぁ\",82],[\"ab41\",\"첔첕첖첗첚첛첝첞첟첡\",6,\"첪첮\",5,\"첶첷첹\"],[\"ab61\",\"첺첻첽\",6,\"쳆쳈쳊\",5,\"쳑쳒쳓쳕\",5],[\"ab81\",\"쳛\",8,\"쳥\",6,\"쳭쳮쳯쳱\",12,\"ァ\",85],[\"ac41\",\"쳾쳿촀촂\",5,\"촊촋촍촎촏촑\",6,\"촚촜촞촟촠\"],[\"ac61\",\"촡촢촣촥촦촧촩촪촫촭\",11,\"촺\",4],[\"ac81\",\"촿\",28,\"쵝쵞쵟А\",5,\"ЁЖ\",25],[\"acd1\",\"а\",5,\"ёж\",25],[\"ad41\",\"쵡쵢쵣쵥\",6,\"쵮쵰쵲\",5,\"쵹\",7],[\"ad61\",\"춁\",6,\"춉\",10,\"춖춗춙춚춛춝춞춟\"],[\"ad81\",\"춠춡춢춣춦춨춪\",5,\"춱\",18,\"췅\"],[\"ae41\",\"췆\",5,\"췍췎췏췑\",16],[\"ae61\",\"췢\",5,\"췩췪췫췭췮췯췱\",6,\"췺췼췾\",4],[\"ae81\",\"츃츅츆츇츉츊츋츍\",6,\"츕츖츗츘츚\",5,\"츢츣츥츦츧츩츪츫\"],[\"af41\",\"츬츭츮츯츲츴츶\",19],[\"af61\",\"칊\",13,\"칚칛칝칞칢\",5,\"칪칬\"],[\"af81\",\"칮\",5,\"칶칷칹칺칻칽\",6,\"캆캈캊\",5,\"캒캓캕캖캗캙\"],[\"b041\",\"캚\",5,\"캢캦\",5,\"캮\",12],[\"b061\",\"캻\",5,\"컂\",19],[\"b081\",\"컖\",13,\"컦컧컩컪컭\",6,\"컶컺\",5,\"가각간갇갈갉갊감\",7,\"같\",4,\"갠갤갬갭갯갰갱갸갹갼걀걋걍걔걘걜거걱건걷걸걺검겁것겄겅겆겉겊겋게겐겔겜겝겟겠겡겨격겪견겯결겸겹겻겼경곁계곈곌곕곗고곡곤곧골곪곬곯곰곱곳공곶과곽관괄괆\"],[\"b141\",\"켂켃켅켆켇켉\",6,\"켒켔켖\",5,\"켝켞켟켡켢켣\"],[\"b161\",\"켥\",6,\"켮켲\",5,\"켹\",11],[\"b181\",\"콅\",14,\"콖콗콙콚콛콝\",6,\"콦콨콪콫콬괌괍괏광괘괜괠괩괬괭괴괵괸괼굄굅굇굉교굔굘굡굣구국군굳굴굵굶굻굼굽굿궁궂궈궉권궐궜궝궤궷귀귁귄귈귐귑귓규균귤그극근귿글긁금급긋긍긔기긱긴긷길긺김깁깃깅깆깊까깍깎깐깔깖깜깝깟깠깡깥깨깩깬깰깸\"],[\"b241\",\"콭콮콯콲콳콵콶콷콹\",6,\"쾁쾂쾃쾄쾆\",5,\"쾍\"],[\"b261\",\"쾎\",18,\"쾢\",5,\"쾩\"],[\"b281\",\"쾪\",5,\"쾱\",18,\"쿅\",6,\"깹깻깼깽꺄꺅꺌꺼꺽꺾껀껄껌껍껏껐껑께껙껜껨껫껭껴껸껼꼇꼈꼍꼐꼬꼭꼰꼲꼴꼼꼽꼿꽁꽂꽃꽈꽉꽐꽜꽝꽤꽥꽹꾀꾄꾈꾐꾑꾕꾜꾸꾹꾼꿀꿇꿈꿉꿋꿍꿎꿔꿜꿨꿩꿰꿱꿴꿸뀀뀁뀄뀌뀐뀔뀜뀝뀨끄끅끈끊끌끎끓끔끕끗끙\"],[\"b341\",\"쿌\",19,\"쿢쿣쿥쿦쿧쿩\"],[\"b361\",\"쿪\",5,\"쿲쿴쿶\",5,\"쿽쿾쿿퀁퀂퀃퀅\",5],[\"b381\",\"퀋\",5,\"퀒\",5,\"퀙\",19,\"끝끼끽낀낄낌낍낏낑나낙낚난낟날낡낢남납낫\",4,\"낱낳내낵낸낼냄냅냇냈냉냐냑냔냘냠냥너넉넋넌널넒넓넘넙넛넜넝넣네넥넨넬넴넵넷넸넹녀녁년녈념녑녔녕녘녜녠노녹논놀놂놈놉놋농높놓놔놘놜놨뇌뇐뇔뇜뇝\"],[\"b441\",\"퀮\",5,\"퀶퀷퀹퀺퀻퀽\",6,\"큆큈큊\",5],[\"b461\",\"큑큒큓큕큖큗큙\",6,\"큡\",10,\"큮큯\"],[\"b481\",\"큱큲큳큵\",6,\"큾큿킀킂\",18,\"뇟뇨뇩뇬뇰뇹뇻뇽누눅눈눋눌눔눕눗눙눠눴눼뉘뉜뉠뉨뉩뉴뉵뉼늄늅늉느늑는늘늙늚늠늡늣능늦늪늬늰늴니닉닌닐닒님닙닛닝닢다닥닦단닫\",4,\"닳담답닷\",4,\"닿대댁댄댈댐댑댓댔댕댜더덕덖던덛덜덞덟덤덥\"],[\"b541\",\"킕\",14,\"킦킧킩킪킫킭\",5],[\"b561\",\"킳킶킸킺\",5,\"탂탃탅탆탇탊\",5,\"탒탖\",4],[\"b581\",\"탛탞탟탡탢탣탥\",6,\"탮탲\",5,\"탹\",11,\"덧덩덫덮데덱덴델뎀뎁뎃뎄뎅뎌뎐뎔뎠뎡뎨뎬도독돈돋돌돎돐돔돕돗동돛돝돠돤돨돼됐되된될됨됩됫됴두둑둔둘둠둡둣둥둬뒀뒈뒝뒤뒨뒬뒵뒷뒹듀듄듈듐듕드득든듣들듦듬듭듯등듸디딕딘딛딜딤딥딧딨딩딪따딱딴딸\"],[\"b641\",\"턅\",7,\"턎\",17],[\"b661\",\"턠\",15,\"턲턳턵턶턷턹턻턼턽턾\"],[\"b681\",\"턿텂텆\",5,\"텎텏텑텒텓텕\",6,\"텞텠텢\",5,\"텩텪텫텭땀땁땃땄땅땋때땍땐땔땜땝땟땠땡떠떡떤떨떪떫떰떱떳떴떵떻떼떽뗀뗄뗌뗍뗏뗐뗑뗘뗬또똑똔똘똥똬똴뙈뙤뙨뚜뚝뚠뚤뚫뚬뚱뛔뛰뛴뛸뜀뜁뜅뜨뜩뜬뜯뜰뜸뜹뜻띄띈띌띔띕띠띤띨띰띱띳띵라락란랄람랍랏랐랑랒랖랗\"],[\"b741\",\"텮\",13,\"텽\",6,\"톅톆톇톉톊\"],[\"b761\",\"톋\",20,\"톢톣톥톦톧\"],[\"b781\",\"톩\",6,\"톲톴톶톷톸톹톻톽톾톿퇁\",14,\"래랙랜랠램랩랫랬랭랴략랸럇량러럭런럴럼럽럿렀렁렇레렉렌렐렘렙렛렝려력련렬렴렵렷렸령례롄롑롓로록론롤롬롭롯롱롸롼뢍뢨뢰뢴뢸룀룁룃룅료룐룔룝룟룡루룩룬룰룸룹룻룽뤄뤘뤠뤼뤽륀륄륌륏륑류륙륜률륨륩\"],[\"b841\",\"퇐\",7,\"퇙\",17],[\"b861\",\"퇫\",8,\"퇵퇶퇷퇹\",13],[\"b881\",\"툈툊\",5,\"툑\",24,\"륫륭르륵른를름릅릇릉릊릍릎리릭린릴림립릿링마막만많\",4,\"맘맙맛망맞맡맣매맥맨맬맴맵맷맸맹맺먀먁먈먕머먹먼멀멂멈멉멋멍멎멓메멕멘멜멤멥멧멨멩며멱면멸몃몄명몇몌모목몫몬몰몲몸몹못몽뫄뫈뫘뫙뫼\"],[\"b941\",\"툪툫툮툯툱툲툳툵\",6,\"툾퉀퉂\",5,\"퉉퉊퉋퉌\"],[\"b961\",\"퉍\",14,\"퉝\",6,\"퉥퉦퉧퉨\"],[\"b981\",\"퉩\",22,\"튂튃튅튆튇튉튊튋튌묀묄묍묏묑묘묜묠묩묫무묵묶문묻물묽묾뭄뭅뭇뭉뭍뭏뭐뭔뭘뭡뭣뭬뮈뮌뮐뮤뮨뮬뮴뮷므믄믈믐믓미믹민믿밀밂밈밉밋밌밍및밑바\",4,\"받\",4,\"밤밥밧방밭배백밴밸뱀뱁뱃뱄뱅뱉뱌뱍뱐뱝버벅번벋벌벎범법벗\"],[\"ba41\",\"튍튎튏튒튓튔튖\",5,\"튝튞튟튡튢튣튥\",6,\"튭\"],[\"ba61\",\"튮튯튰튲\",5,\"튺튻튽튾틁틃\",4,\"틊틌\",5],[\"ba81\",\"틒틓틕틖틗틙틚틛틝\",6,\"틦\",9,\"틲틳틵틶틷틹틺벙벚베벡벤벧벨벰벱벳벴벵벼벽변별볍볏볐병볕볘볜보복볶본볼봄봅봇봉봐봔봤봬뵀뵈뵉뵌뵐뵘뵙뵤뵨부북분붇불붉붊붐붑붓붕붙붚붜붤붰붸뷔뷕뷘뷜뷩뷰뷴뷸븀븃븅브븍븐블븜븝븟비빅빈빌빎빔빕빗빙빚빛빠빡빤\"],[\"bb41\",\"틻\",4,\"팂팄팆\",5,\"팏팑팒팓팕팗\",4,\"팞팢팣\"],[\"bb61\",\"팤팦팧팪팫팭팮팯팱\",6,\"팺팾\",5,\"퍆퍇퍈퍉\"],[\"bb81\",\"퍊\",31,\"빨빪빰빱빳빴빵빻빼빽뺀뺄뺌뺍뺏뺐뺑뺘뺙뺨뻐뻑뻔뻗뻘뻠뻣뻤뻥뻬뼁뼈뼉뼘뼙뼛뼜뼝뽀뽁뽄뽈뽐뽑뽕뾔뾰뿅뿌뿍뿐뿔뿜뿟뿡쀼쁑쁘쁜쁠쁨쁩삐삑삔삘삠삡삣삥사삭삯산삳살삵삶삼삽삿샀상샅새색샌샐샘샙샛샜생샤\"],[\"bc41\",\"퍪\",17,\"퍾퍿펁펂펃펅펆펇\"],[\"bc61\",\"펈펉펊펋펎펒\",5,\"펚펛펝펞펟펡\",6,\"펪펬펮\"],[\"bc81\",\"펯\",4,\"펵펶펷펹펺펻펽\",6,\"폆폇폊\",5,\"폑\",5,\"샥샨샬샴샵샷샹섀섄섈섐섕서\",4,\"섣설섦섧섬섭섯섰성섶세섹센셀셈셉셋셌셍셔셕션셜셤셥셧셨셩셰셴셸솅소속솎손솔솖솜솝솟송솥솨솩솬솰솽쇄쇈쇌쇔쇗쇘쇠쇤쇨쇰쇱쇳쇼쇽숀숄숌숍숏숑수숙순숟술숨숩숫숭\"],[\"bd41\",\"폗폙\",7,\"폢폤\",7,\"폮폯폱폲폳폵폶폷\"],[\"bd61\",\"폸폹폺폻폾퐀퐂\",5,\"퐉\",13],[\"bd81\",\"퐗\",5,\"퐞\",25,\"숯숱숲숴쉈쉐쉑쉔쉘쉠쉥쉬쉭쉰쉴쉼쉽쉿슁슈슉슐슘슛슝스슥슨슬슭슴습슷승시식신싣실싫심십싯싱싶싸싹싻싼쌀쌈쌉쌌쌍쌓쌔쌕쌘쌜쌤쌥쌨쌩썅써썩썬썰썲썸썹썼썽쎄쎈쎌쏀쏘쏙쏜쏟쏠쏢쏨쏩쏭쏴쏵쏸쐈쐐쐤쐬쐰\"],[\"be41\",\"퐸\",7,\"푁푂푃푅\",14],[\"be61\",\"푔\",7,\"푝푞푟푡푢푣푥\",7,\"푮푰푱푲\"],[\"be81\",\"푳\",4,\"푺푻푽푾풁풃\",4,\"풊풌풎\",5,\"풕\",8,\"쐴쐼쐽쑈쑤쑥쑨쑬쑴쑵쑹쒀쒔쒜쒸쒼쓩쓰쓱쓴쓸쓺쓿씀씁씌씐씔씜씨씩씬씰씸씹씻씽아악안앉않알앍앎앓암압앗았앙앝앞애액앤앨앰앱앳앴앵야약얀얄얇얌얍얏양얕얗얘얜얠얩어억언얹얻얼얽얾엄\",6,\"엌엎\"],[\"bf41\",\"풞\",10,\"풪\",14],[\"bf61\",\"풹\",18,\"퓍퓎퓏퓑퓒퓓퓕\"],[\"bf81\",\"퓖\",5,\"퓝퓞퓠\",7,\"퓩퓪퓫퓭퓮퓯퓱\",6,\"퓹퓺퓼에엑엔엘엠엡엣엥여역엮연열엶엷염\",5,\"옅옆옇예옌옐옘옙옛옜오옥온올옭옮옰옳옴옵옷옹옻와왁완왈왐왑왓왔왕왜왝왠왬왯왱외왹왼욀욈욉욋욍요욕욘욜욤욥욧용우욱운울욹욺움웁웃웅워웍원월웜웝웠웡웨\"],[\"c041\",\"퓾\",5,\"픅픆픇픉픊픋픍\",6,\"픖픘\",5],[\"c061\",\"픞\",25],[\"c081\",\"픸픹픺픻픾픿핁핂핃핅\",6,\"핎핐핒\",5,\"핚핛핝핞핟핡핢핣웩웬웰웸웹웽위윅윈윌윔윕윗윙유육윤율윰윱윳융윷으윽은을읊음읍읏응\",7,\"읜읠읨읫이익인일읽읾잃임입잇있잉잊잎자작잔잖잗잘잚잠잡잣잤장잦재잭잰잴잼잽잿쟀쟁쟈쟉쟌쟎쟐쟘쟝쟤쟨쟬저적전절젊\"],[\"c141\",\"핤핦핧핪핬핮\",5,\"핶핷핹핺핻핽\",6,\"햆햊햋\"],[\"c161\",\"햌햍햎햏햑\",19,\"햦햧\"],[\"c181\",\"햨\",31,\"점접젓정젖제젝젠젤젬젭젯젱져젼졀졈졉졌졍졔조족존졸졺좀좁좃종좆좇좋좌좍좔좝좟좡좨좼좽죄죈죌죔죕죗죙죠죡죤죵주죽준줄줅줆줌줍줏중줘줬줴쥐쥑쥔쥘쥠쥡쥣쥬쥰쥴쥼즈즉즌즐즘즙즛증지직진짇질짊짐집짓\"],[\"c241\",\"헊헋헍헎헏헑헓\",4,\"헚헜헞\",5,\"헦헧헩헪헫헭헮\"],[\"c261\",\"헯\",4,\"헶헸헺\",5,\"혂혃혅혆혇혉\",6,\"혒\"],[\"c281\",\"혖\",5,\"혝혞혟혡혢혣혥\",7,\"혮\",9,\"혺혻징짖짙짚짜짝짠짢짤짧짬짭짯짰짱째짹짼쨀쨈쨉쨋쨌쨍쨔쨘쨩쩌쩍쩐쩔쩜쩝쩟쩠쩡쩨쩽쪄쪘쪼쪽쫀쫄쫌쫍쫏쫑쫓쫘쫙쫠쫬쫴쬈쬐쬔쬘쬠쬡쭁쭈쭉쭌쭐쭘쭙쭝쭤쭸쭹쮜쮸쯔쯤쯧쯩찌찍찐찔찜찝찡찢찧차착찬찮찰참찹찻\"],[\"c341\",\"혽혾혿홁홂홃홄홆홇홊홌홎홏홐홒홓홖홗홙홚홛홝\",4],[\"c361\",\"홢\",4,\"홨홪\",5,\"홲홳홵\",11],[\"c381\",\"횁횂횄횆\",5,\"횎횏횑횒횓횕\",7,\"횞횠횢\",5,\"횩횪찼창찾채책챈챌챔챕챗챘챙챠챤챦챨챰챵처척천철첨첩첫첬청체첵첸첼쳄쳅쳇쳉쳐쳔쳤쳬쳰촁초촉촌촐촘촙촛총촤촨촬촹최쵠쵤쵬쵭쵯쵱쵸춈추축춘출춤춥춧충춰췄췌췐취췬췰췸췹췻췽츄츈츌츔츙츠측츤츨츰츱츳층\"],[\"c441\",\"횫횭횮횯횱\",7,\"횺횼\",7,\"훆훇훉훊훋\"],[\"c461\",\"훍훎훏훐훒훓훕훖훘훚\",5,\"훡훢훣훥훦훧훩\",4],[\"c481\",\"훮훯훱훲훳훴훶\",5,\"훾훿휁휂휃휅\",11,\"휒휓휔치칙친칟칠칡침칩칫칭카칵칸칼캄캅캇캉캐캑캔캘캠캡캣캤캥캬캭컁커컥컨컫컬컴컵컷컸컹케켁켄켈켐켑켓켕켜켠켤켬켭켯켰켱켸코콕콘콜콤콥콧콩콰콱콴콸쾀쾅쾌쾡쾨쾰쿄쿠쿡쿤쿨쿰쿱쿳쿵쿼퀀퀄퀑퀘퀭퀴퀵퀸퀼\"],[\"c541\",\"휕휖휗휚휛휝휞휟휡\",6,\"휪휬휮\",5,\"휶휷휹\"],[\"c561\",\"휺휻휽\",6,\"흅흆흈흊\",5,\"흒흓흕흚\",4],[\"c581\",\"흟흢흤흦흧흨흪흫흭흮흯흱흲흳흵\",6,\"흾흿힀힂\",5,\"힊힋큄큅큇큉큐큔큘큠크큭큰클큼큽킁키킥킨킬킴킵킷킹타탁탄탈탉탐탑탓탔탕태택탠탤탬탭탯탰탱탸턍터턱턴털턺텀텁텃텄텅테텍텐텔템텝텟텡텨텬텼톄톈토톡톤톨톰톱톳통톺톼퇀퇘퇴퇸툇툉툐투툭툰툴툼툽툿퉁퉈퉜\"],[\"c641\",\"힍힎힏힑\",6,\"힚힜힞\",5],[\"c6a1\",\"퉤튀튁튄튈튐튑튕튜튠튤튬튱트특튼튿틀틂틈틉틋틔틘틜틤틥티틱틴틸팀팁팃팅파팍팎판팔팖팜팝팟팠팡팥패팩팬팰팸팹팻팼팽퍄퍅퍼퍽펀펄펌펍펏펐펑페펙펜펠펨펩펫펭펴편펼폄폅폈평폐폘폡폣포폭폰폴폼폽폿퐁\"],[\"c7a1\",\"퐈퐝푀푄표푠푤푭푯푸푹푼푿풀풂품풉풋풍풔풩퓌퓐퓔퓜퓟퓨퓬퓰퓸퓻퓽프픈플픔픕픗피픽핀필핌핍핏핑하학한할핥함합핫항해핵핸핼햄햅햇했행햐향허헉헌헐헒험헙헛헝헤헥헨헬헴헵헷헹혀혁현혈혐협혓혔형혜혠\"],[\"c8a1\",\"혤혭호혹혼홀홅홈홉홋홍홑화확환활홧황홰홱홴횃횅회획횐횔횝횟횡효횬횰횹횻후훅훈훌훑훔훗훙훠훤훨훰훵훼훽휀휄휑휘휙휜휠휨휩휫휭휴휵휸휼흄흇흉흐흑흔흖흗흘흙흠흡흣흥흩희흰흴흼흽힁히힉힌힐힘힙힛힝\"],[\"caa1\",\"伽佳假價加可呵哥嘉嫁家暇架枷柯歌珂痂稼苛茄街袈訶賈跏軻迦駕刻却各恪慤殼珏脚覺角閣侃刊墾奸姦干幹懇揀杆柬桿澗癎看磵稈竿簡肝艮艱諫間乫喝曷渴碣竭葛褐蝎鞨勘坎堪嵌感憾戡敢柑橄減甘疳監瞰紺邯鑑鑒龕\"],[\"cba1\",\"匣岬甲胛鉀閘剛堈姜岡崗康强彊慷江畺疆糠絳綱羌腔舡薑襁講鋼降鱇介价個凱塏愷愾慨改槪漑疥皆盖箇芥蓋豈鎧開喀客坑更粳羹醵倨去居巨拒据據擧渠炬祛距踞車遽鉅鋸乾件健巾建愆楗腱虔蹇鍵騫乞傑杰桀儉劍劒檢\"],[\"cca1\",\"瞼鈐黔劫怯迲偈憩揭擊格檄激膈覡隔堅牽犬甄絹繭肩見譴遣鵑抉決潔結缺訣兼慊箝謙鉗鎌京俓倞傾儆勁勍卿坰境庚徑慶憬擎敬景暻更梗涇炅烱璟璥瓊痙硬磬竟競絅經耕耿脛莖警輕逕鏡頃頸驚鯨係啓堺契季屆悸戒桂械\"],[\"cda1\",\"棨溪界癸磎稽系繫繼計誡谿階鷄古叩告呱固姑孤尻庫拷攷故敲暠枯槁沽痼皐睾稿羔考股膏苦苽菰藁蠱袴誥賈辜錮雇顧高鼓哭斛曲梏穀谷鵠困坤崑昆梱棍滾琨袞鯤汨滑骨供公共功孔工恐恭拱控攻珙空蚣貢鞏串寡戈果瓜\"],[\"cea1\",\"科菓誇課跨過鍋顆廓槨藿郭串冠官寬慣棺款灌琯瓘管罐菅觀貫關館刮恝括适侊光匡壙廣曠洸炚狂珖筐胱鑛卦掛罫乖傀塊壞怪愧拐槐魁宏紘肱轟交僑咬喬嬌嶠巧攪敎校橋狡皎矯絞翹膠蕎蛟較轎郊餃驕鮫丘久九仇俱具勾\"],[\"cfa1\",\"區口句咎嘔坵垢寇嶇廐懼拘救枸柩構歐毆毬求溝灸狗玖球瞿矩究絿耉臼舅舊苟衢謳購軀逑邱鉤銶駒驅鳩鷗龜國局菊鞠鞫麴君窘群裙軍郡堀屈掘窟宮弓穹窮芎躬倦券勸卷圈拳捲權淃眷厥獗蕨蹶闕机櫃潰詭軌饋句晷歸貴\"],[\"d0a1\",\"鬼龜叫圭奎揆槻珪硅窺竅糾葵規赳逵閨勻均畇筠菌鈞龜橘克剋劇戟棘極隙僅劤勤懃斤根槿瑾筋芹菫覲謹近饉契今妗擒昑檎琴禁禽芩衾衿襟金錦伋及急扱汲級給亘兢矜肯企伎其冀嗜器圻基埼夔奇妓寄岐崎己幾忌技旗旣\"],[\"d1a1\",\"朞期杞棋棄機欺氣汽沂淇玘琦琪璂璣畸畿碁磯祁祇祈祺箕紀綺羈耆耭肌記譏豈起錡錤飢饑騎騏驥麒緊佶吉拮桔金喫儺喇奈娜懦懶拏拿癩\",5,\"那樂\",4,\"諾酪駱亂卵暖欄煖爛蘭難鸞捏捺南嵐枏楠湳濫男藍襤拉\"],[\"d2a1\",\"納臘蠟衲囊娘廊\",4,\"乃來內奈柰耐冷女年撚秊念恬拈捻寧寗努勞奴弩怒擄櫓爐瑙盧\",5,\"駑魯\",10,\"濃籠聾膿農惱牢磊腦賂雷尿壘\",7,\"嫩訥杻紐勒\",5,\"能菱陵尼泥匿溺多茶\"],[\"d3a1\",\"丹亶但單團壇彖斷旦檀段湍短端簞緞蛋袒鄲鍛撻澾獺疸達啖坍憺擔曇淡湛潭澹痰聃膽蕁覃談譚錟沓畓答踏遝唐堂塘幢戇撞棠當糖螳黨代垈坮大對岱帶待戴擡玳臺袋貸隊黛宅德悳倒刀到圖堵塗導屠島嶋度徒悼挑掉搗桃\"],[\"d4a1\",\"棹櫂淘渡滔濤燾盜睹禱稻萄覩賭跳蹈逃途道都鍍陶韜毒瀆牘犢獨督禿篤纛讀墩惇敦旽暾沌焞燉豚頓乭突仝冬凍動同憧東桐棟洞潼疼瞳童胴董銅兜斗杜枓痘竇荳讀豆逗頭屯臀芚遁遯鈍得嶝橙燈登等藤謄鄧騰喇懶拏癩羅\"],[\"d5a1\",\"蘿螺裸邏樂洛烙珞絡落諾酪駱丹亂卵欄欒瀾爛蘭鸞剌辣嵐擥攬欖濫籃纜藍襤覽拉臘蠟廊朗浪狼琅瑯螂郞來崍徠萊冷掠略亮倆兩凉梁樑粮粱糧良諒輛量侶儷勵呂廬慮戾旅櫚濾礪藜蠣閭驢驪麗黎力曆歷瀝礫轢靂憐戀攣漣\"],[\"d6a1\",\"煉璉練聯蓮輦連鍊冽列劣洌烈裂廉斂殮濂簾獵令伶囹寧岺嶺怜玲笭羚翎聆逞鈴零靈領齡例澧禮醴隷勞怒撈擄櫓潞瀘爐盧老蘆虜路輅露魯鷺鹵碌祿綠菉錄鹿麓論壟弄朧瀧瓏籠聾儡瀨牢磊賂賚賴雷了僚寮廖料燎療瞭聊蓼\"],[\"d7a1\",\"遼鬧龍壘婁屢樓淚漏瘻累縷蔞褸鏤陋劉旒柳榴流溜瀏琉瑠留瘤硫謬類六戮陸侖倫崙淪綸輪律慄栗率隆勒肋凜凌楞稜綾菱陵俚利厘吏唎履悧李梨浬犁狸理璃異痢籬罹羸莉裏裡里釐離鯉吝潾燐璘藺躪隣鱗麟林淋琳臨霖砬\"],[\"d8a1\",\"立笠粒摩瑪痲碼磨馬魔麻寞幕漠膜莫邈万卍娩巒彎慢挽晩曼滿漫灣瞞萬蔓蠻輓饅鰻唜抹末沫茉襪靺亡妄忘忙望網罔芒茫莽輞邙埋妹媒寐昧枚梅每煤罵買賣邁魅脈貊陌驀麥孟氓猛盲盟萌冪覓免冕勉棉沔眄眠綿緬面麵滅\"],[\"d9a1\",\"蔑冥名命明暝椧溟皿瞑茗蓂螟酩銘鳴袂侮冒募姆帽慕摸摹暮某模母毛牟牡瑁眸矛耗芼茅謀謨貌木沐牧目睦穆鶩歿沒夢朦蒙卯墓妙廟描昴杳渺猫竗苗錨務巫憮懋戊拇撫无楙武毋無珷畝繆舞茂蕪誣貿霧鵡墨默們刎吻問文\"],[\"daa1\",\"汶紊紋聞蚊門雯勿沕物味媚尾嵋彌微未梶楣渼湄眉米美薇謎迷靡黴岷悶愍憫敏旻旼民泯玟珉緡閔密蜜謐剝博拍搏撲朴樸泊珀璞箔粕縛膊舶薄迫雹駁伴半反叛拌搬攀斑槃泮潘班畔瘢盤盼磐磻礬絆般蟠返頒飯勃拔撥渤潑\"],[\"dba1\",\"發跋醱鉢髮魃倣傍坊妨尨幇彷房放方旁昉枋榜滂磅紡肪膀舫芳蒡蚌訪謗邦防龐倍俳北培徘拜排杯湃焙盃背胚裴裵褙賠輩配陪伯佰帛柏栢白百魄幡樊煩燔番磻繁蕃藩飜伐筏罰閥凡帆梵氾汎泛犯範范法琺僻劈壁擘檗璧癖\"],[\"dca1\",\"碧蘗闢霹便卞弁變辨辯邊別瞥鱉鼈丙倂兵屛幷昞昺柄棅炳甁病秉竝輧餠騈保堡報寶普步洑湺潽珤甫菩補褓譜輔伏僕匐卜宓復服福腹茯蔔複覆輹輻馥鰒本乶俸奉封峯峰捧棒烽熢琫縫蓬蜂逢鋒鳳不付俯傅剖副否咐埠夫婦\"],[\"dda1\",\"孚孵富府復扶敷斧浮溥父符簿缶腐腑膚艀芙莩訃負賦賻赴趺部釜阜附駙鳧北分吩噴墳奔奮忿憤扮昐汾焚盆粉糞紛芬賁雰不佛弗彿拂崩朋棚硼繃鵬丕備匕匪卑妃婢庇悲憊扉批斐枇榧比毖毗毘沸泌琵痺砒碑秕秘粃緋翡肥\"],[\"dea1\",\"脾臂菲蜚裨誹譬費鄙非飛鼻嚬嬪彬斌檳殯浜濱瀕牝玭貧賓頻憑氷聘騁乍事些仕伺似使俟僿史司唆嗣四士奢娑寫寺射巳師徙思捨斜斯柶査梭死沙泗渣瀉獅砂社祀祠私篩紗絲肆舍莎蓑蛇裟詐詞謝賜赦辭邪飼駟麝削數朔索\"],[\"dfa1\",\"傘刪山散汕珊産疝算蒜酸霰乷撒殺煞薩三參杉森渗芟蔘衫揷澁鈒颯上傷像償商喪嘗孀尙峠常床庠廂想桑橡湘爽牀狀相祥箱翔裳觴詳象賞霜塞璽賽嗇塞穡索色牲生甥省笙墅壻嶼序庶徐恕抒捿敍暑曙書栖棲犀瑞筮絮緖署\"],[\"e0a1\",\"胥舒薯西誓逝鋤黍鼠夕奭席惜昔晳析汐淅潟石碩蓆釋錫仙僊先善嬋宣扇敾旋渲煽琁瑄璇璿癬禪線繕羨腺膳船蘚蟬詵跣選銑鐥饍鮮卨屑楔泄洩渫舌薛褻設說雪齧剡暹殲纖蟾贍閃陝攝涉燮葉城姓宬性惺成星晟猩珹盛省筬\"],[\"e1a1\",\"聖聲腥誠醒世勢歲洗稅笹細說貰召嘯塑宵小少巢所掃搔昭梳沼消溯瀟炤燒甦疏疎瘙笑篠簫素紹蔬蕭蘇訴逍遡邵銷韶騷俗屬束涑粟續謖贖速孫巽損蓀遜飡率宋悚松淞訟誦送頌刷殺灑碎鎖衰釗修受嗽囚垂壽嫂守岫峀帥愁\"],[\"e2a1\",\"戍手授搜收數樹殊水洙漱燧狩獸琇璲瘦睡秀穗竪粹綏綬繡羞脩茱蒐蓚藪袖誰讐輸遂邃酬銖銹隋隧隨雖需須首髓鬚叔塾夙孰宿淑潚熟琡璹肅菽巡徇循恂旬栒楯橓殉洵淳珣盾瞬筍純脣舜荀蓴蕣詢諄醇錞順馴戌術述鉥崇崧\"],[\"e3a1\",\"嵩瑟膝蝨濕拾習褶襲丞乘僧勝升承昇繩蠅陞侍匙嘶始媤尸屎屍市弑恃施是時枾柴猜矢示翅蒔蓍視試詩諡豕豺埴寔式息拭植殖湜熄篒蝕識軾食飾伸侁信呻娠宸愼新晨燼申神紳腎臣莘薪藎蜃訊身辛辰迅失室實悉審尋心沁\"],[\"e4a1\",\"沈深瀋甚芯諶什十拾雙氏亞俄兒啞娥峨我牙芽莪蛾衙訝阿雅餓鴉鵝堊岳嶽幄惡愕握樂渥鄂鍔顎鰐齷安岸按晏案眼雁鞍顔鮟斡謁軋閼唵岩巖庵暗癌菴闇壓押狎鴨仰央怏昻殃秧鴦厓哀埃崖愛曖涯碍艾隘靄厄扼掖液縊腋額\"],[\"e5a1\",\"櫻罌鶯鸚也倻冶夜惹揶椰爺耶若野弱掠略約若葯蒻藥躍亮佯兩凉壤孃恙揚攘敭暘梁楊樣洋瀁煬痒瘍禳穰糧羊良襄諒讓釀陽量養圄御於漁瘀禦語馭魚齬億憶抑檍臆偃堰彦焉言諺孼蘖俺儼嚴奄掩淹嶪業円予余勵呂女如廬\"],[\"e6a1\",\"旅歟汝濾璵礖礪與艅茹輿轝閭餘驪麗黎亦力域役易曆歷疫繹譯轢逆驛嚥堧姸娟宴年延憐戀捐挻撚椽沇沿涎涓淵演漣烟然煙煉燃燕璉硏硯秊筵緣練縯聯衍軟輦蓮連鉛鍊鳶列劣咽悅涅烈熱裂說閱厭廉念捻染殮炎焰琰艶苒\"],[\"e7a1\",\"簾閻髥鹽曄獵燁葉令囹塋寧嶺嶸影怜映暎楹榮永泳渶潁濚瀛瀯煐營獰玲瑛瑩瓔盈穎纓羚聆英詠迎鈴鍈零霙靈領乂倪例刈叡曳汭濊猊睿穢芮藝蘂禮裔詣譽豫醴銳隸霓預五伍俉傲午吾吳嗚塢墺奧娛寤悟惡懊敖旿晤梧汚澳\"],[\"e8a1\",\"烏熬獒筽蜈誤鰲鼇屋沃獄玉鈺溫瑥瘟穩縕蘊兀壅擁瓮甕癰翁邕雍饔渦瓦窩窪臥蛙蝸訛婉完宛梡椀浣玩琓琬碗緩翫脘腕莞豌阮頑曰往旺枉汪王倭娃歪矮外嵬巍猥畏了僚僥凹堯夭妖姚寥寮尿嶢拗搖撓擾料曜樂橈燎燿瑤療\"],[\"e9a1\",\"窈窯繇繞耀腰蓼蟯要謠遙遼邀饒慾欲浴縟褥辱俑傭冗勇埇墉容庸慂榕涌湧溶熔瑢用甬聳茸蓉踊鎔鏞龍于佑偶優又友右宇寓尤愚憂旴牛玗瑀盂祐禑禹紆羽芋藕虞迂遇郵釪隅雨雩勖彧旭昱栯煜稶郁頊云暈橒殞澐熉耘芸蕓\"],[\"eaa1\",\"運隕雲韻蔚鬱亐熊雄元原員圓園垣媛嫄寃怨愿援沅洹湲源爰猿瑗苑袁轅遠阮院願鴛月越鉞位偉僞危圍委威尉慰暐渭爲瑋緯胃萎葦蔿蝟衛褘謂違韋魏乳侑儒兪劉唯喩孺宥幼幽庾悠惟愈愉揄攸有杻柔柚柳楡楢油洧流游溜\"],[\"eba1\",\"濡猶猷琉瑜由留癒硫紐維臾萸裕誘諛諭踰蹂遊逾遺酉釉鍮類六堉戮毓肉育陸倫允奫尹崙淪潤玧胤贇輪鈗閏律慄栗率聿戎瀜絨融隆垠恩慇殷誾銀隱乙吟淫蔭陰音飮揖泣邑凝應膺鷹依倚儀宜意懿擬椅毅疑矣義艤薏蟻衣誼\"],[\"eca1\",\"議醫二以伊利吏夷姨履已弛彛怡易李梨泥爾珥理異痍痢移罹而耳肄苡荑裏裡貽貳邇里離飴餌匿溺瀷益翊翌翼謚人仁刃印吝咽因姻寅引忍湮燐璘絪茵藺蚓認隣靭靷鱗麟一佚佾壹日溢逸鎰馹任壬妊姙恁林淋稔臨荏賃入卄\"],[\"eda1\",\"立笠粒仍剩孕芿仔刺咨姉姿子字孜恣慈滋炙煮玆瓷疵磁紫者自茨蔗藉諮資雌作勺嚼斫昨灼炸爵綽芍酌雀鵲孱棧殘潺盞岑暫潛箴簪蠶雜丈仗匠場墻壯奬將帳庄張掌暲杖樟檣欌漿牆狀獐璋章粧腸臟臧莊葬蔣薔藏裝贓醬長\"],[\"eea1\",\"障再哉在宰才材栽梓渽滓災縡裁財載齋齎爭箏諍錚佇低儲咀姐底抵杵楮樗沮渚狙猪疽箸紵苧菹著藷詛貯躇這邸雎齟勣吊嫡寂摘敵滴狄炙的積笛籍績翟荻謫賊赤跡蹟迪迹適鏑佃佺傳全典前剪塡塼奠專展廛悛戰栓殿氈澱\"],[\"efa1\",\"煎琠田甸畑癲筌箋箭篆纏詮輾轉鈿銓錢鐫電顚顫餞切截折浙癤竊節絶占岾店漸点粘霑鮎點接摺蝶丁井亭停偵呈姃定幀庭廷征情挺政整旌晶晸柾楨檉正汀淀淨渟湞瀞炡玎珽町睛碇禎程穽精綎艇訂諪貞鄭酊釘鉦鋌錠霆靖\"],[\"f0a1\",\"靜頂鼎制劑啼堤帝弟悌提梯濟祭第臍薺製諸蹄醍除際霽題齊俎兆凋助嘲弔彫措操早晁曺曹朝條棗槽漕潮照燥爪璪眺祖祚租稠窕粗糟組繰肇藻蚤詔調趙躁造遭釣阻雕鳥族簇足鏃存尊卒拙猝倧宗從悰慫棕淙琮種終綜縱腫\"],[\"f1a1\",\"踪踵鍾鐘佐坐左座挫罪主住侏做姝胄呪周嗾奏宙州廚晝朱柱株注洲湊澍炷珠疇籌紂紬綢舟蛛註誅走躊輳週酎酒鑄駐竹粥俊儁准埈寯峻晙樽浚準濬焌畯竣蠢逡遵雋駿茁中仲衆重卽櫛楫汁葺增憎曾拯烝甑症繒蒸證贈之只\"],[\"f2a1\",\"咫地址志持指摯支旨智枝枳止池沚漬知砥祉祗紙肢脂至芝芷蜘誌識贄趾遲直稙稷織職唇嗔塵振搢晉晋桭榛殄津溱珍瑨璡畛疹盡眞瞋秦縉縝臻蔯袗診賑軫辰進鎭陣陳震侄叱姪嫉帙桎瓆疾秩窒膣蛭質跌迭斟朕什執潗緝輯\"],[\"f3a1\",\"鏶集徵懲澄且侘借叉嗟嵯差次此磋箚茶蹉車遮捉搾着窄錯鑿齪撰澯燦璨瓚竄簒纂粲纘讚贊鑽餐饌刹察擦札紮僭參塹慘慙懺斬站讒讖倉倡創唱娼廠彰愴敞昌昶暢槍滄漲猖瘡窓脹艙菖蒼債埰寀寨彩採砦綵菜蔡采釵冊柵策\"],[\"f4a1\",\"責凄妻悽處倜刺剔尺慽戚拓擲斥滌瘠脊蹠陟隻仟千喘天川擅泉淺玔穿舛薦賤踐遷釧闡阡韆凸哲喆徹撤澈綴輟轍鐵僉尖沾添甛瞻簽籤詹諂堞妾帖捷牒疊睫諜貼輒廳晴淸聽菁請靑鯖切剃替涕滯締諦逮遞體初剿哨憔抄招梢\"],[\"f5a1\",\"椒楚樵炒焦硝礁礎秒稍肖艸苕草蕉貂超酢醋醮促囑燭矗蜀觸寸忖村邨叢塚寵悤憁摠總聰蔥銃撮催崔最墜抽推椎楸樞湫皺秋芻萩諏趨追鄒酋醜錐錘鎚雛騶鰍丑畜祝竺筑築縮蓄蹙蹴軸逐春椿瑃出朮黜充忠沖蟲衝衷悴膵萃\"],[\"f6a1\",\"贅取吹嘴娶就炊翠聚脆臭趣醉驟鷲側仄厠惻測層侈値嗤峙幟恥梔治淄熾痔痴癡稚穉緇緻置致蚩輜雉馳齒則勅飭親七柒漆侵寢枕沈浸琛砧針鍼蟄秤稱快他咤唾墮妥惰打拖朶楕舵陀馱駝倬卓啄坼度托拓擢晫柝濁濯琢琸託\"],[\"f7a1\",\"鐸呑嘆坦彈憚歎灘炭綻誕奪脫探眈耽貪塔搭榻宕帑湯糖蕩兌台太怠態殆汰泰笞胎苔跆邰颱宅擇澤撑攄兎吐土討慟桶洞痛筒統通堆槌腿褪退頹偸套妬投透鬪慝特闖坡婆巴把播擺杷波派爬琶破罷芭跛頗判坂板版瓣販辦鈑\"],[\"f8a1\",\"阪八叭捌佩唄悖敗沛浿牌狽稗覇貝彭澎烹膨愎便偏扁片篇編翩遍鞭騙貶坪平枰萍評吠嬖幣廢弊斃肺蔽閉陛佈包匍匏咆哺圃布怖抛抱捕暴泡浦疱砲胞脯苞葡蒲袍褒逋鋪飽鮑幅暴曝瀑爆輻俵剽彪慓杓標漂瓢票表豹飇飄驃\"],[\"f9a1\",\"品稟楓諷豊風馮彼披疲皮被避陂匹弼必泌珌畢疋筆苾馝乏逼下何厦夏廈昰河瑕荷蝦賀遐霞鰕壑學虐謔鶴寒恨悍旱汗漢澣瀚罕翰閑閒限韓割轄函含咸啣喊檻涵緘艦銜陷鹹合哈盒蛤閤闔陜亢伉姮嫦巷恒抗杭桁沆港缸肛航\"],[\"faa1\",\"行降項亥偕咳垓奚孩害懈楷海瀣蟹解該諧邂駭骸劾核倖幸杏荇行享向嚮珦鄕響餉饗香噓墟虛許憲櫶獻軒歇險驗奕爀赫革俔峴弦懸晛泫炫玄玹現眩睍絃絢縣舷衒見賢鉉顯孑穴血頁嫌俠協夾峽挾浹狹脅脇莢鋏頰亨兄刑型\"],[\"fba1\",\"形泂滎瀅灐炯熒珩瑩荊螢衡逈邢鎣馨兮彗惠慧暳蕙蹊醯鞋乎互呼壕壺好岵弧戶扈昊晧毫浩淏湖滸澔濠濩灝狐琥瑚瓠皓祜糊縞胡芦葫蒿虎號蝴護豪鎬頀顥惑或酷婚昏混渾琿魂忽惚笏哄弘汞泓洪烘紅虹訌鴻化和嬅樺火畵\"],[\"fca1\",\"禍禾花華話譁貨靴廓擴攫確碻穫丸喚奐宦幻患換歡晥桓渙煥環紈還驩鰥活滑猾豁闊凰幌徨恍惶愰慌晃晄榥況湟滉潢煌璜皇篁簧荒蝗遑隍黃匯回廻徊恢悔懷晦會檜淮澮灰獪繪膾茴蛔誨賄劃獲宖橫鐄哮嚆孝效斅曉梟涍淆\"],[\"fda1\",\"爻肴酵驍侯候厚后吼喉嗅帿後朽煦珝逅勛勳塤壎焄熏燻薰訓暈薨喧暄煊萱卉喙毁彙徽揮暉煇諱輝麾休携烋畦虧恤譎鷸兇凶匈洶胸黑昕欣炘痕吃屹紇訖欠欽歆吸恰洽翕興僖凞喜噫囍姬嬉希憙憘戱晞曦熙熹熺犧禧稀羲詰\"]]");

/***/ }),
/* 404 */
/***/ (function(module) {

module.exports = JSON.parse("[[\"8740\",\"䏰䰲䘃䖦䕸𧉧䵷䖳𧲱䳢𧳅㮕䜶䝄䱇䱀𤊿𣘗𧍒𦺋𧃒䱗𪍑䝏䗚䲅𧱬䴇䪤䚡𦬣爥𥩔𡩣𣸆𣽡晍囻\"],[\"8767\",\"綕夝𨮹㷴霴𧯯寛𡵞媤㘥𩺰嫑宷峼杮薓𩥅瑡璝㡵𡵓𣚞𦀡㻬\"],[\"87a1\",\"𥣞㫵竼龗𤅡𨤍𣇪𠪊𣉞䌊蒄龖鐯䤰蘓墖靊鈘秐稲晠権袝瑌篅枂稬剏遆㓦珄𥶹瓆鿇垳䤯呌䄱𣚎堘穲𧭥讏䚮𦺈䆁𥶙箮𢒼鿈𢓁𢓉𢓌鿉蔄𣖻䂴鿊䓡𪷿拁灮鿋\"],[\"8840\",\"㇀\",4,\"𠄌㇅𠃑𠃍㇆㇇𠃋𡿨㇈𠃊㇉㇊㇋㇌𠄎㇍㇎ĀÁǍÀĒÉĚÈŌÓǑÒ࿿Ê̄Ế࿿Ê̌ỀÊāáǎàɑēéěèīíǐìōóǒòūúǔùǖǘǚ\"],[\"88a1\",\"ǜü࿿ê̄ế࿿ê̌ềêɡ⏚⏛\"],[\"8940\",\"𪎩𡅅\"],[\"8943\",\"攊\"],[\"8946\",\"丽滝鵎釟\"],[\"894c\",\"𧜵撑会伨侨兖兴农凤务动医华发变团声处备夲头学实実岚庆总斉柾栄桥济炼电纤纬纺织经统缆缷艺苏药视设询车轧轮\"],[\"89a1\",\"琑糼緍楆竉刧\"],[\"89ab\",\"醌碸酞肼\"],[\"89b0\",\"贋胶𠧧\"],[\"89b5\",\"肟黇䳍鷉鸌䰾𩷶𧀎鸊𪄳㗁\"],[\"89c1\",\"溚舾甙\"],[\"89c5\",\"䤑马骏龙禇𨑬𡷊𠗐𢫦两亁亀亇亿仫伷㑌侽㹈倃傈㑽㒓㒥円夅凛凼刅争剹劐匧㗇厩㕑厰㕓参吣㕭㕲㚁咓咣咴咹哐哯唘唣唨㖘唿㖥㖿嗗㗅\"],[\"8a40\",\"𧶄唥\"],[\"8a43\",\"𠱂𠴕𥄫喐𢳆㧬𠍁蹆𤶸𩓥䁓𨂾睺𢰸㨴䟕𨅝𦧲𤷪擝𠵼𠾴𠳕𡃴撍蹾𠺖𠰋𠽤𢲩𨉖𤓓\"],[\"8a64\",\"𠵆𩩍𨃩䟴𤺧𢳂骲㩧𩗴㿭㔆𥋇𩟔𧣈𢵄鵮頕\"],[\"8a76\",\"䏙𦂥撴哣𢵌𢯊𡁷㧻𡁯\"],[\"8aa1\",\"𦛚𦜖𧦠擪𥁒𠱃蹨𢆡𨭌𠜱\"],[\"8aac\",\"䠋𠆩㿺塳𢶍\"],[\"8ab2\",\"𤗈𠓼𦂗𠽌𠶖啹䂻䎺\"],[\"8abb\",\"䪴𢩦𡂝膪飵𠶜捹㧾𢝵跀嚡摼㹃\"],[\"8ac9\",\"𪘁𠸉𢫏𢳉\"],[\"8ace\",\"𡃈𣧂㦒㨆𨊛㕸𥹉𢃇噒𠼱𢲲𩜠㒼氽𤸻\"],[\"8adf\",\"𧕴𢺋𢈈𪙛𨳍𠹺𠰴𦠜羓𡃏𢠃𢤹㗻𥇣𠺌𠾍𠺪㾓𠼰𠵇𡅏𠹌\"],[\"8af6\",\"𠺫𠮩𠵈𡃀𡄽㿹𢚖搲𠾭\"],[\"8b40\",\"𣏴𧘹𢯎𠵾𠵿𢱑𢱕㨘𠺘𡃇𠼮𪘲𦭐𨳒𨶙𨳊閪哌苄喹\"],[\"8b55\",\"𩻃鰦骶𧝞𢷮煀腭胬尜𦕲脴㞗卟𨂽醶𠻺𠸏𠹷𠻻㗝𤷫㘉𠳖嚯𢞵𡃉𠸐𠹸𡁸𡅈𨈇𡑕𠹹𤹐𢶤婔𡀝𡀞𡃵𡃶垜𠸑\"],[\"8ba1\",\"𧚔𨋍𠾵𠹻𥅾㜃𠾶𡆀𥋘𪊽𤧚𡠺𤅷𨉼墙剨㘚𥜽箲孨䠀䬬鼧䧧鰟鮍𥭴𣄽嗻㗲嚉丨夂𡯁屮靑𠂆乛亻㔾尣彑忄㣺扌攵歺氵氺灬爫丬犭𤣩罒礻糹罓𦉪㓁\"],[\"8bde\",\"𦍋耂肀𦘒𦥑卝衤见𧢲讠贝钅镸长门𨸏韦页风飞饣𩠐鱼鸟黄歯龜丷𠂇阝户钢\"],[\"8c40\",\"倻淾𩱳龦㷉袏𤅎灷峵䬠𥇍㕙𥴰愢𨨲辧釶熑朙玺𣊁𪄇㲋𡦀䬐磤琂冮𨜏䀉橣𪊺䈣蘏𠩯稪𩥇𨫪靕灍匤𢁾鏴盙𨧣龧矝亣俰傼丯众龨吴綋墒壐𡶶庒庙忂𢜒斋\"],[\"8ca1\",\"𣏹椙橃𣱣泿\"],[\"8ca7\",\"爀𤔅玌㻛𤨓嬕璹讃𥲤𥚕窓篬糃繬苸薗龩袐龪躹龫迏蕟駠鈡龬𨶹𡐿䁱䊢娚\"],[\"8cc9\",\"顨杫䉶圽\"],[\"8cce\",\"藖𤥻芿𧄍䲁𦵴嵻𦬕𦾾龭龮宖龯曧繛湗秊㶈䓃𣉖𢞖䎚䔶\"],[\"8ce6\",\"峕𣬚諹屸㴒𣕑嵸龲煗䕘𤃬𡸣䱷㥸㑊𠆤𦱁諌侴𠈹妿腬顖𩣺弻\"],[\"8d40\",\"𠮟\"],[\"8d42\",\"𢇁𨥭䄂䚻𩁹㼇龳𪆵䃸㟖䛷𦱆䅼𨚲𧏿䕭㣔𥒚䕡䔛䶉䱻䵶䗪㿈𤬏㙡䓞䒽䇭崾嵈嵖㷼㠏嶤嶹㠠㠸幂庽弥徃㤈㤔㤿㥍惗愽峥㦉憷憹懏㦸戬抐拥挘㧸嚱\"],[\"8da1\",\"㨃揢揻搇摚㩋擀崕嘡龟㪗斆㪽旿晓㫲暒㬢朖㭂枤栀㭘桊梄㭲㭱㭻椉楃牜楤榟榅㮼槖㯝橥橴橱檂㯬檙㯲檫檵櫔櫶殁毁毪汵沪㳋洂洆洦涁㳯涤涱渕渘温溆𨧀溻滢滚齿滨滩漤漴㵆𣽁澁澾㵪㵵熷岙㶊瀬㶑灐灔灯灿炉𠌥䏁㗱𠻘\"],[\"8e40\",\"𣻗垾𦻓焾𥟠㙎榢𨯩孴穉𥣡𩓙穥穽𥦬窻窰竂竃燑𦒍䇊竚竝竪䇯咲𥰁笋筕笩𥌎𥳾箢筯莜𥮴𦱿篐萡箒箸𥴠㶭𥱥蒒篺簆簵𥳁籄粃𤢂粦晽𤕸糉糇糦籴糳糵糎\"],[\"8ea1\",\"繧䔝𦹄絝𦻖璍綉綫焵綳緒𤁗𦀩緤㴓緵𡟹緥𨍭縝𦄡𦅚繮纒䌫鑬縧罀罁罇礶𦋐駡羗𦍑羣𡙡𠁨䕜𣝦䔃𨌺翺𦒉者耈耝耨耯𪂇𦳃耻耼聡𢜔䦉𦘦𣷣𦛨朥肧𨩈脇脚墰𢛶汿𦒘𤾸擧𡒊舘𡡞橓𤩥𤪕䑺舩𠬍𦩒𣵾俹𡓽蓢荢𦬊𤦧𣔰𡝳𣷸芪椛芳䇛\"],[\"8f40\",\"蕋苐茚𠸖𡞴㛁𣅽𣕚艻苢茘𣺋𦶣𦬅𦮗𣗎㶿茝嗬莅䔋𦶥莬菁菓㑾𦻔橗蕚㒖𦹂𢻯葘𥯤葱㷓䓤檧葊𣲵祘蒨𦮖𦹷𦹃蓞萏莑䒠蒓蓤𥲑䉀𥳀䕃蔴嫲𦺙䔧蕳䔖枿蘖\"],[\"8fa1\",\"𨘥𨘻藁𧂈蘂𡖂𧃍䕫䕪蘨㙈𡢢号𧎚虾蝱𪃸蟮𢰧螱蟚蠏噡虬桖䘏衅衆𧗠𣶹𧗤衞袜䙛袴袵揁装睷𧜏覇覊覦覩覧覼𨨥觧𧤤𧪽誜瞓釾誐𧩙竩𧬺𣾏䜓𧬸煼謌謟𥐰𥕥謿譌譍誩𤩺讐讛誯𡛟䘕衏貛𧵔𧶏貫㜥𧵓賖𧶘𧶽贒贃𡤐賛灜贑𤳉㻐起\"],[\"9040\",\"趩𨀂𡀔𤦊㭼𨆼𧄌竧躭躶軃鋔輙輭𨍥𨐒辥錃𪊟𠩐辳䤪𨧞𨔽𣶻廸𣉢迹𪀔𨚼𨔁𢌥㦀𦻗逷𨔼𧪾遡𨕬𨘋邨𨜓郄𨛦邮都酧㫰醩釄粬𨤳𡺉鈎沟鉁鉢𥖹銹𨫆𣲛𨬌𥗛\"],[\"90a1\",\"𠴱錬鍫𨫡𨯫炏嫃𨫢𨫥䥥鉄𨯬𨰹𨯿鍳鑛躼閅閦鐦閠濶䊹𢙺𨛘𡉼𣸮䧟氜陻隖䅬隣𦻕懚隶磵𨫠隽双䦡𦲸𠉴𦐐𩂯𩃥𤫑𡤕𣌊霱虂霶䨏䔽䖅𤫩灵孁霛靜𩇕靗孊𩇫靟鐥僐𣂷𣂼鞉鞟鞱鞾韀韒韠𥑬韮琜𩐳響韵𩐝𧥺䫑頴頳顋顦㬎𧅵㵑𠘰𤅜\"],[\"9140\",\"𥜆飊颷飈飇䫿𦴧𡛓喰飡飦飬鍸餹𤨩䭲𩡗𩤅駵騌騻騐驘𥜥㛄𩂱𩯕髠髢𩬅髴䰎鬔鬭𨘀倴鬴𦦨㣃𣁽魐魀𩴾婅𡡣鮎𤉋鰂鯿鰌𩹨鷔𩾷𪆒𪆫𪃡𪄣𪇟鵾鶃𪄴鸎梈\"],[\"91a1\",\"鷄𢅛𪆓𪈠𡤻𪈳鴹𪂹𪊴麐麕麞麢䴴麪麯𤍤黁㭠㧥㴝伲㞾𨰫鼂鼈䮖鐤𦶢鼗鼖鼹嚟嚊齅馸𩂋韲葿齢齩竜龎爖䮾𤥵𤦻煷𤧸𤍈𤩑玞𨯚𡣺禟𨥾𨸶鍩鏳𨩄鋬鎁鏋𨥬𤒹爗㻫睲穃烐𤑳𤏸煾𡟯炣𡢾𣖙㻇𡢅𥐯𡟸㜢𡛻𡠹㛡𡝴𡣑𥽋㜣𡛀坛𤨥𡏾𡊨\"],[\"9240\",\"𡏆𡒶蔃𣚦蔃葕𤦔𧅥𣸱𥕜𣻻𧁒䓴𣛮𩦝𦼦柹㜳㰕㷧塬𡤢栐䁗𣜿𤃡𤂋𤄏𦰡哋嚞𦚱嚒𠿟𠮨𠸍鏆𨬓鎜仸儫㠙𤐶亼𠑥𠍿佋侊𥙑婨𠆫𠏋㦙𠌊𠐔㐵伩𠋀𨺳𠉵諚𠈌亘\"],[\"92a1\",\"働儍侢伃𤨎𣺊佂倮偬傁俌俥偘僼兙兛兝兞湶𣖕𣸹𣺿浲𡢄𣺉冨凃𠗠䓝𠒣𠒒𠒑赺𨪜𠜎剙劤𠡳勡鍮䙺熌𤎌𠰠𤦬𡃤槑𠸝瑹㻞璙琔瑖玘䮎𤪼𤂍叐㖄爏𤃉喴𠍅响𠯆圝鉝雴鍦埝垍坿㘾壋媙𨩆𡛺𡝯𡜐娬妸銏婾嫏娒𥥆𡧳𡡡𤊕㛵洅瑃娡𥺃\"],[\"9340\",\"媁𨯗𠐓鏠璌𡌃焅䥲鐈𨧻鎽㞠尞岞幞幈𡦖𡥼𣫮廍孏𡤃𡤄㜁𡢠㛝𡛾㛓脪𨩇𡶺𣑲𨦨弌弎𡤧𡞫婫𡜻孄蘔𧗽衠恾𢡠𢘫忛㺸𢖯𢖾𩂈𦽳懀𠀾𠁆𢘛憙憘恵𢲛𢴇𤛔𩅍\"],[\"93a1\",\"摱𤙥𢭪㨩𢬢𣑐𩣪𢹸挷𪑛撶挱揑𤧣𢵧护𢲡搻敫楲㯴𣂎𣊭𤦉𣊫唍𣋠𡣙𩐿曎𣊉𣆳㫠䆐𥖄𨬢𥖏𡛼𥕛𥐥磮𣄃𡠪𣈴㑤𣈏𣆂𤋉暎𦴤晫䮓昰𧡰𡷫晣𣋒𣋡昞𥡲㣑𣠺𣞼㮙𣞢𣏾瓐㮖枏𤘪梶栞㯄檾㡣𣟕𤒇樳橒櫉欅𡤒攑梘橌㯗橺歗𣿀𣲚鎠鋲𨯪𨫋\"],[\"9440\",\"銉𨀞𨧜鑧涥漋𤧬浧𣽿㶏渄𤀼娽渊塇洤硂焻𤌚𤉶烱牐犇犔𤞏𤜥兹𤪤𠗫瑺𣻸𣙟𤩊𤤗𥿡㼆㺱𤫟𨰣𣼵悧㻳瓌琼鎇琷䒟𦷪䕑疃㽣𤳙𤴆㽘畕癳𪗆㬙瑨𨫌𤦫𤦎㫻\"],[\"94a1\",\"㷍𤩎㻿𤧅𤣳釺圲鍂𨫣𡡤僟𥈡𥇧睸𣈲眎眏睻𤚗𣞁㩞𤣰琸璛㺿𤪺𤫇䃈𤪖𦆮錇𥖁砞碍碈磒珐祙𧝁𥛣䄎禛蒖禥樭𣻺稺秴䅮𡛦䄲鈵秱𠵌𤦌𠊙𣶺𡝮㖗啫㕰㚪𠇔𠰍竢婙𢛵𥪯𥪜娍𠉛磰娪𥯆竾䇹籝籭䈑𥮳𥺼𥺦糍𤧹𡞰粎籼粮檲緜縇緓罎𦉡\"],[\"9540\",\"𦅜𧭈綗𥺂䉪𦭵𠤖柖𠁎𣗏埄𦐒𦏸𤥢翝笧𠠬𥫩𥵃笌𥸎駦虅驣樜𣐿㧢𤧷𦖭騟𦖠蒀𧄧𦳑䓪脷䐂胆脉腂𦞴飃𦩂艢艥𦩑葓𦶧蘐𧈛媆䅿𡡀嬫𡢡嫤𡣘蚠蜨𣶏蠭𧐢娂\"],[\"95a1\",\"衮佅袇袿裦襥襍𥚃襔𧞅𧞄𨯵𨯙𨮜𨧹㺭蒣䛵䛏㟲訽訜𩑈彍鈫𤊄旔焩烄𡡅鵭貟賩𧷜妚矃姰䍮㛔踪躧𤰉輰轊䋴汘澻𢌡䢛潹溋𡟚鯩㚵𤤯邻邗啱䤆醻鐄𨩋䁢𨫼鐧𨰝𨰻蓥訫閙閧閗閖𨴴瑅㻂𤣿𤩂𤏪㻧𣈥随𨻧𨹦𨹥㻌𤧭𤩸𣿮琒瑫㻼靁𩂰\"],[\"9640\",\"桇䨝𩂓𥟟靝鍨𨦉𨰦𨬯𦎾銺嬑譩䤼珹𤈛鞛靱餸𠼦巁𨯅𤪲頟𩓚鋶𩗗釥䓀𨭐𤩧𨭤飜𨩅㼀鈪䤥萔餻饍𧬆㷽馛䭯馪驜𨭥𥣈檏騡嫾騯𩣱䮐𩥈馼䮽䮗鍽塲𡌂堢𤦸\"],[\"96a1\",\"𡓨硄𢜟𣶸棅㵽鑘㤧慐𢞁𢥫愇鱏鱓鱻鰵鰐魿鯏𩸭鮟𪇵𪃾鴡䲮𤄄鸘䲰鴌𪆴𪃭𪃳𩤯鶥蒽𦸒𦿟𦮂藼䔳𦶤𦺄𦷰萠藮𦸀𣟗𦁤秢𣖜𣙀䤭𤧞㵢鏛銾鍈𠊿碹鉷鑍俤㑀遤𥕝砽硔碶硋𡝗𣇉𤥁㚚佲濚濙瀞瀞吔𤆵垻壳垊鴖埗焴㒯𤆬燫𦱀𤾗嬨𡞵𨩉\"],[\"9740\",\"愌嫎娋䊼𤒈㜬䭻𨧼鎻鎸𡣖𠼝葲𦳀𡐓𤋺𢰦𤏁妔𣶷𦝁綨𦅛𦂤𤦹𤦋𨧺鋥珢㻩璴𨭣𡢟㻡𤪳櫘珳珻㻖𤨾𤪔𡟙𤩦𠎧𡐤𤧥瑈𤤖炥𤥶銄珦鍟𠓾錱𨫎𨨖鎆𨯧𥗕䤵𨪂煫\"],[\"97a1\",\"𤥃𠳿嚤𠘚𠯫𠲸唂秄𡟺緾𡛂𤩐𡡒䔮鐁㜊𨫀𤦭妰𡢿𡢃𧒄媡㛢𣵛㚰鉟婹𨪁𡡢鍴㳍𠪴䪖㦊僴㵩㵌𡎜煵䋻𨈘渏𩃤䓫浗𧹏灧沯㳖𣿭𣸭渂漌㵯𠏵畑㚼㓈䚀㻚䡱姄鉮䤾轁𨰜𦯀堒埈㛖𡑒烾𤍢𤩱𢿣𡊰𢎽梹楧𡎘𣓥𧯴𣛟𨪃𣟖𣏺𤲟樚𣚭𦲷萾䓟䓎\"],[\"9840\",\"𦴦𦵑𦲂𦿞漗𧄉茽𡜺菭𦲀𧁓𡟛妉媂𡞳婡婱𡤅𤇼㜭姯𡜼㛇熎鎐暚𤊥婮娫𤊓樫𣻹𧜶𤑛𤋊焝𤉙𨧡侰𦴨峂𤓎𧹍𤎽樌𤉖𡌄炦焳𤏩㶥泟勇𤩏繥姫崯㷳彜𤩝𡟟綤萦\"],[\"98a1\",\"咅𣫺𣌀𠈔坾𠣕𠘙㿥𡾞𪊶瀃𩅛嵰玏糓𨩙𩐠俈翧狍猐𧫴猸猹𥛶獁獈㺩𧬘遬燵𤣲珡臶㻊県㻑沢国琙琞琟㻢㻰㻴㻺瓓㼎㽓畂畭畲疍㽼痈痜㿀癍㿗癴㿜発𤽜熈嘣覀塩䀝睃䀹条䁅㗛瞘䁪䁯属瞾矋売砘点砜䂨砹硇硑硦葈𥔵礳栃礲䄃\"],[\"9940\",\"䄉禑禙辻稆込䅧窑䆲窼艹䇄竏竛䇏両筢筬筻簒簛䉠䉺类粜䊌粸䊔糭输烀𠳏総緔緐緽羮羴犟䎗耠耥笹耮耱联㷌垴炠肷胩䏭脌猪脎脒畠脔䐁㬹腖腙腚\"],[\"99a1\",\"䐓堺腼膄䐥膓䐭膥埯臁臤艔䒏芦艶苊苘苿䒰荗险榊萅烵葤惣蒈䔄蒾蓡蓸蔐蔸蕒䔻蕯蕰藠䕷虲蚒蚲蛯际螋䘆䘗袮裿褤襇覑𧥧訩訸誔誴豑賔賲贜䞘塟跃䟭仮踺嗘坔蹱嗵躰䠷軎転軤軭軲辷迁迊迌逳駄䢭飠鈓䤞鈨鉘鉫銱銮銿\"],[\"9a40\",\"鋣鋫鋳鋴鋽鍃鎄鎭䥅䥑麿鐗匁鐝鐭鐾䥪鑔鑹锭関䦧间阳䧥枠䨤靀䨵鞲韂噔䫤惨颹䬙飱塄餎餙冴餜餷饂饝饢䭰駅䮝騼鬏窃魩鮁鯝鯱鯴䱭鰠㝯𡯂鵉鰺\"],[\"9aa1\",\"黾噐鶓鶽鷀鷼银辶鹻麬麱麽黆铜黢黱黸竈齄𠂔𠊷𠎠椚铃妬𠓗塀铁㞹𠗕𠘕𠙶𡚺块煳𠫂𠫍𠮿呪吆𠯋咞𠯻𠰻𠱓𠱥𠱼惧𠲍噺𠲵𠳝𠳭𠵯𠶲𠷈楕鰯螥𠸄𠸎𠻗𠾐𠼭𠹳尠𠾼帋𡁜𡁏𡁶朞𡁻𡂈𡂖㙇𡂿𡃓𡄯𡄻卤蒭𡋣𡍵𡌶讁𡕷𡘙𡟃𡟇乸炻𡠭𡥪\"],[\"9b40\",\"𡨭𡩅𡰪𡱰𡲬𡻈拃𡻕𡼕熘桕𢁅槩㛈𢉼𢏗𢏺𢜪𢡱𢥏苽𢥧𢦓𢫕覥𢫨辠𢬎鞸𢬿顇骽𢱌\"],[\"9b62\",\"𢲈𢲷𥯨𢴈𢴒𢶷𢶕𢹂𢽴𢿌𣀳𣁦𣌟𣏞徱晈暿𧩹𣕧𣗳爁𤦺矗𣘚𣜖纇𠍆墵朎\"],[\"9ba1\",\"椘𣪧𧙗𥿢𣸑𣺹𧗾𢂚䣐䪸𤄙𨪚𤋮𤌍𤀻𤌴𤎖𤩅𠗊凒𠘑妟𡺨㮾𣳿𤐄𤓖垈𤙴㦛𤜯𨗨𩧉㝢𢇃譞𨭎駖𤠒𤣻𤨕爉𤫀𠱸奥𤺥𤾆𠝹軚𥀬劏圿煱𥊙𥐙𣽊𤪧喼𥑆𥑮𦭒釔㑳𥔿𧘲𥕞䜘𥕢𥕦𥟇𤤿𥡝偦㓻𣏌惞𥤃䝼𨥈𥪮𥮉𥰆𡶐垡煑澶𦄂𧰒遖𦆲𤾚譢𦐂𦑊\"],[\"9c40\",\"嵛𦯷輶𦒄𡤜諪𤧶𦒈𣿯𦔒䯀𦖿𦚵𢜛鑥𥟡憕娧晉侻嚹𤔡𦛼乪𤤴陖涏𦲽㘘襷𦞙𦡮𦐑𦡞營𦣇筂𩃀𠨑𦤦鄄𦤹穅鷰𦧺騦𦨭㙟𦑩𠀡禃𦨴𦭛崬𣔙菏𦮝䛐𦲤画补𦶮墶\"],[\"9ca1\",\"㜜𢖍𧁋𧇍㱔𧊀𧊅銁𢅺𧊋錰𧋦𤧐氹钟𧑐𠻸蠧裵𢤦𨑳𡞱溸𤨪𡠠㦤㚹尐秣䔿暶𩲭𩢤襃𧟌𧡘囖䃟𡘊㦡𣜯𨃨𡏅熭荦𧧝𩆨婧䲷𧂯𨦫𧧽𧨊𧬋𧵦𤅺筃祾𨀉澵𪋟樃𨌘厢𦸇鎿栶靝𨅯𨀣𦦵𡏭𣈯𨁈嶅𨰰𨂃圕頣𨥉嶫𤦈斾槕叒𤪥𣾁㰑朶𨂐𨃴𨄮𡾡𨅏\"],[\"9d40\",\"𨆉𨆯𨈚𨌆𨌯𨎊㗊𨑨𨚪䣺揦𨥖砈鉕𨦸䏲𨧧䏟𨧨𨭆𨯔姸𨰉輋𨿅𩃬筑𩄐𩄼㷷𩅞𤫊运犏嚋𩓧𩗩𩖰𩖸𩜲𩣑𩥉𩥪𩧃𩨨𩬎𩵚𩶛纟𩻸𩼣䲤镇𪊓熢𪋿䶑递𪗋䶜𠲜达嗁\"],[\"9da1\",\"辺𢒰边𤪓䔉繿潖檱仪㓤𨬬𧢝㜺躀𡟵𨀤𨭬𨮙𧨾𦚯㷫𧙕𣲷𥘵𥥖亚𥺁𦉘嚿𠹭踎孭𣺈𤲞揞拐𡟶𡡻攰嘭𥱊吚𥌑㷆𩶘䱽嘢嘞罉𥻘奵𣵀蝰东𠿪𠵉𣚺脗鵞贘瘻鱅癎瞹鍅吲腈苷嘥脲萘肽嗪祢噃吖𠺝㗎嘅嗱曱𨋢㘭甴嗰喺咗啲𠱁𠲖廐𥅈𠹶𢱢\"],[\"9e40\",\"𠺢麫絚嗞𡁵抝靭咔賍燶酶揼掹揾啩𢭃鱲𢺳冚㓟𠶧冧呍唞唓癦踭𦢊疱肶蠄螆裇膶萜𡃁䓬猄𤜆宐茋𦢓噻𢛴𧴯𤆣𧵳𦻐𧊶酰𡇙鈈𣳼𪚩𠺬𠻹牦𡲢䝎𤿂𧿹𠿫䃺\"],[\"9ea1\",\"鱝攟𢶠䣳𤟠𩵼𠿬𠸊恢𧖣𠿭\"],[\"9ead\",\"𦁈𡆇熣纎鵐业丄㕷嬍沲卧㚬㧜卽㚥𤘘墚𤭮舭呋垪𥪕𠥹\"],[\"9ec5\",\"㩒𢑥獴𩺬䴉鯭𣳾𩼰䱛𤾩𩖞𩿞葜𣶶𧊲𦞳𣜠挮紥𣻷𣸬㨪逈勌㹴㙺䗩𠒎癀嫰𠺶硺𧼮墧䂿噼鮋嵴癔𪐴麅䳡痹㟻愙𣃚𤏲\"],[\"9ef5\",\"噝𡊩垧𤥣𩸆刴𧂮㖭汊鵼\"],[\"9f40\",\"籖鬹埞𡝬屓擓𩓐𦌵𧅤蚭𠴨𦴢𤫢𠵱\"],[\"9f4f\",\"凾𡼏嶎霃𡷑麁遌笟鬂峑箣扨挵髿篏鬪籾鬮籂粆鰕篼鬉鼗鰛𤤾齚啳寃俽麘俲剠㸆勑坧偖妷帒韈鶫轜呩鞴饀鞺匬愰\"],[\"9fa1\",\"椬叚鰊鴂䰻陁榀傦畆𡝭駚剳\"],[\"9fae\",\"酙隁酜\"],[\"9fb2\",\"酑𨺗捿𦴣櫊嘑醎畺抅𠏼獏籰𥰡𣳽\"],[\"9fc1\",\"𤤙盖鮝个𠳔莾衂\"],[\"9fc9\",\"届槀僭坺刟巵从氱𠇲伹咜哚劚趂㗾弌㗳\"],[\"9fdb\",\"歒酼龥鮗頮颴骺麨麄煺笔\"],[\"9fe7\",\"毺蠘罸\"],[\"9feb\",\"嘠𪙊蹷齓\"],[\"9ff0\",\"跔蹏鸜踁抂𨍽踨蹵竓𤩷稾磘泪詧瘇\"],[\"a040\",\"𨩚鼦泎蟖痃𪊲硓咢贌狢獱謭猂瓱賫𤪻蘯徺袠䒷\"],[\"a055\",\"𡠻𦸅\"],[\"a058\",\"詾𢔛\"],[\"a05b\",\"惽癧髗鵄鍮鮏蟵\"],[\"a063\",\"蠏賷猬霡鮰㗖犲䰇籑饊𦅙慙䰄麖慽\"],[\"a073\",\"坟慯抦戹拎㩜懢厪𣏵捤栂㗒\"],[\"a0a1\",\"嵗𨯂迚𨸹\"],[\"a0a6\",\"僙𡵆礆匲阸𠼻䁥\"],[\"a0ae\",\"矾\"],[\"a0b0\",\"糂𥼚糚稭聦聣絍甅瓲覔舚朌聢𧒆聛瓰脃眤覉𦟌畓𦻑螩蟎臈螌詉貭譃眫瓸蓚㘵榲趦\"],[\"a0d4\",\"覩瑨涹蟁𤀑瓧㷛煶悤憜㳑煢恷\"],[\"a0e2\",\"罱𨬭牐惩䭾删㰘𣳇𥻗𧙖𥔱𡥄𡋾𩤃𦷜𧂭峁𦆭𨨏𣙷𠃮𦡆𤼎䕢嬟𦍌齐麦𦉫\"],[\"a3c0\",\"␀\",31,\"␡\"],[\"c6a1\",\"①\",9,\"⑴\",9,\"ⅰ\",9,\"丶丿亅亠冂冖冫勹匸卩厶夊宀巛⼳广廴彐彡攴无疒癶辵隶¨ˆヽヾゝゞ〃仝々〆〇ー［］✽ぁ\",23],[\"c740\",\"す\",58,\"ァアィイ\"],[\"c7a1\",\"ゥ\",81,\"А\",5,\"ЁЖ\",4],[\"c840\",\"Л\",26,\"ёж\",25,\"⇧↸↹㇏𠃌乚𠂊刂䒑\"],[\"c8a1\",\"龰冈龱𧘇\"],[\"c8cd\",\"￢￤＇＂㈱№℡゛゜⺀⺄⺆⺇⺈⺊⺌⺍⺕⺜⺝⺥⺧⺪⺬⺮⺶⺼⺾⻆⻊⻌⻍⻏⻖⻗⻞⻣\"],[\"c8f5\",\"ʃɐɛɔɵœøŋʊɪ\"],[\"f9fe\",\"￭\"],[\"fa40\",\"𠕇鋛𠗟𣿅蕌䊵珯况㙉𤥂𨧤鍄𡧛苮𣳈砼杄拟𤤳𨦪𠊠𦮳𡌅侫𢓭倈𦴩𧪄𣘀𤪱𢔓倩𠍾徤𠎀𠍇滛𠐟偽儁㑺儎顬㝃萖𤦤𠒇兠𣎴兪𠯿𢃼𠋥𢔰𠖎𣈳𡦃宂蝽𠖳𣲙冲冸\"],[\"faa1\",\"鴴凉减凑㳜凓𤪦决凢卂凭菍椾𣜭彻刋刦刼劵剗劔効勅簕蕂勠蘍𦬓包𨫞啉滙𣾀𠥔𣿬匳卄𠯢泋𡜦栛珕恊㺪㣌𡛨燝䒢卭却𨚫卾卿𡖖𡘓矦厓𨪛厠厫厮玧𥝲㽙玜叁叅汉义埾叙㪫𠮏叠𣿫𢶣叶𠱷吓灹唫晗浛呭𦭓𠵴啝咏咤䞦𡜍𠻝㶴𠵍\"],[\"fb40\",\"𨦼𢚘啇䳭启琗喆喩嘅𡣗𤀺䕒𤐵暳𡂴嘷曍𣊊暤暭噍噏磱囱鞇叾圀囯园𨭦㘣𡉏坆𤆥汮炋坂㚱𦱾埦𡐖堃𡑔𤍣堦𤯵塜墪㕡壠壜𡈼壻寿坃𪅐𤉸鏓㖡够梦㛃湙\"],[\"fba1\",\"𡘾娤啓𡚒蔅姉𠵎𦲁𦴪𡟜姙𡟻𡞲𦶦浱𡠨𡛕姹𦹅媫婣㛦𤦩婷㜈媖瑥嫓𦾡𢕔㶅𡤑㜲𡚸広勐孶斈孼𧨎䀄䡝𠈄寕慠𡨴𥧌𠖥寳宝䴐尅𡭄尓珎尔𡲥𦬨屉䣝岅峩峯嶋𡷹𡸷崐崘嵆𡺤岺巗苼㠭𤤁𢁉𢅳芇㠶㯂帮檊幵幺𤒼𠳓厦亷廐厨𡝱帉廴𨒂\"],[\"fc40\",\"廹廻㢠廼栾鐛弍𠇁弢㫞䢮𡌺强𦢈𢏐彘𢑱彣鞽𦹮彲鍀𨨶徧嶶㵟𥉐𡽪𧃸𢙨釖𠊞𨨩怱暅𡡷㥣㷇㘹垐𢞴祱㹀悞悤悳𤦂𤦏𧩓璤僡媠慤萤慂慈𦻒憁凴𠙖憇宪𣾷\"],[\"fca1\",\"𢡟懓𨮝𩥝懐㤲𢦀𢣁怣慜攞掋𠄘担𡝰拕𢸍捬𤧟㨗搸揸𡎎𡟼撐澊𢸶頔𤂌𥜝擡擥鑻㩦携㩗敍漖𤨨𤨣斅敭敟𣁾斵𤥀䬷旑䃘𡠩无旣忟𣐀昘𣇷𣇸晄𣆤𣆥晋𠹵晧𥇦晳晴𡸽𣈱𨗴𣇈𥌓矅𢣷馤朂𤎜𤨡㬫槺𣟂杞杧杢𤇍𩃭柗䓩栢湐鈼栁𣏦𦶠桝\"],[\"fd40\",\"𣑯槡樋𨫟楳棃𣗍椁椀㴲㨁𣘼㮀枬楡𨩊䋼椶榘㮡𠏉荣傐槹𣙙𢄪橅𣜃檝㯳枱櫈𩆜㰍欝𠤣惞欵歴𢟍溵𣫛𠎵𡥘㝀吡𣭚毡𣻼毜氷𢒋𤣱𦭑汚舦汹𣶼䓅𣶽𤆤𤤌𤤀\"],[\"fda1\",\"𣳉㛥㳫𠴲鮃𣇹𢒑羏样𦴥𦶡𦷫涖浜湼漄𤥿𤂅𦹲蔳𦽴凇沜渝萮𨬡港𣸯瑓𣾂秌湏媑𣁋濸㜍澝𣸰滺𡒗𤀽䕕鏰潄潜㵎潴𩅰㴻澟𤅄濓𤂑𤅕𤀹𣿰𣾴𤄿凟𤅖𤅗𤅀𦇝灋灾炧炁烌烕烖烟䄄㷨熴熖𤉷焫煅媈煊煮岜𤍥煏鍢𤋁焬𤑚𤨧𤨢熺𨯨炽爎\"],[\"fe40\",\"鑂爕夑鑃爤鍁𥘅爮牀𤥴梽牕牗㹕𣁄栍漽犂猪猫𤠣𨠫䣭𨠄猨献珏玪𠰺𦨮珉瑉𤇢𡛧𤨤昣㛅𤦷𤦍𤧻珷琕椃𤨦琹𠗃㻗瑜𢢭瑠𨺲瑇珤瑶莹瑬㜰瑴鏱樬璂䥓𤪌\"],[\"fea1\",\"𤅟𤩹𨮏孆𨰃𡢞瓈𡦈甎瓩甞𨻙𡩋寗𨺬鎅畍畊畧畮𤾂㼄𤴓疎瑝疞疴瘂瘬癑癏癯癶𦏵皐臯㟸𦤑𦤎皡皥皷盌𦾟葢𥂝𥅽𡸜眞眦着撯𥈠睘𣊬瞯𨥤𨥨𡛁矴砉𡍶𤨒棊碯磇磓隥礮𥗠磗礴碱𧘌辸袄𨬫𦂃𢘜禆褀椂禀𥡗禝𧬹礼禩渪𧄦㺨秆𩄍秔\"]]");

/***/ }),
/* 405 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var Buffer = __webpack_require__(39).Buffer;

// NOTE: Due to 'stream' module being pretty large (~100Kb, significant in browser environments), 
// we opt to dependency-inject it instead of creating a hard dependency.
module.exports = function(stream_module) {
    var Transform = stream_module.Transform;

    // == Encoder stream =======================================================

    function IconvLiteEncoderStream(conv, options) {
        this.conv = conv;
        options = options || {};
        options.decodeStrings = false; // We accept only strings, so we don't need to decode them.
        Transform.call(this, options);
    }

    IconvLiteEncoderStream.prototype = Object.create(Transform.prototype, {
        constructor: { value: IconvLiteEncoderStream }
    });

    IconvLiteEncoderStream.prototype._transform = function(chunk, encoding, done) {
        if (typeof chunk != 'string')
            return done(new Error("Iconv encoding stream needs strings as its input."));
        try {
            var res = this.conv.write(chunk);
            if (res && res.length) this.push(res);
            done();
        }
        catch (e) {
            done(e);
        }
    }

    IconvLiteEncoderStream.prototype._flush = function(done) {
        try {
            var res = this.conv.end();
            if (res && res.length) this.push(res);
            done();
        }
        catch (e) {
            done(e);
        }
    }

    IconvLiteEncoderStream.prototype.collect = function(cb) {
        var chunks = [];
        this.on('error', cb);
        this.on('data', function(chunk) { chunks.push(chunk); });
        this.on('end', function() {
            cb(null, Buffer.concat(chunks));
        });
        return this;
    }


    // == Decoder stream =======================================================

    function IconvLiteDecoderStream(conv, options) {
        this.conv = conv;
        options = options || {};
        options.encoding = this.encoding = 'utf8'; // We output strings.
        Transform.call(this, options);
    }

    IconvLiteDecoderStream.prototype = Object.create(Transform.prototype, {
        constructor: { value: IconvLiteDecoderStream }
    });

    IconvLiteDecoderStream.prototype._transform = function(chunk, encoding, done) {
        if (!Buffer.isBuffer(chunk) && !(chunk instanceof Uint8Array))
            return done(new Error("Iconv decoding stream needs buffers as its input."));
        try {
            var res = this.conv.write(chunk);
            if (res && res.length) this.push(res, this.encoding);
            done();
        }
        catch (e) {
            done(e);
        }
    }

    IconvLiteDecoderStream.prototype._flush = function(done) {
        try {
            var res = this.conv.end();
            if (res && res.length) this.push(res, this.encoding);                
            done();
        }
        catch (e) {
            done(e);
        }
    }

    IconvLiteDecoderStream.prototype.collect = function(cb) {
        var res = '';
        this.on('error', cb);
        this.on('data', function(chunk) { res += chunk; });
        this.on('end', function() {
            cb(null, res);
        });
        return this;
    }

    return {
        IconvLiteEncoderStream: IconvLiteEncoderStream,
        IconvLiteDecoderStream: IconvLiteDecoderStream,
    };
};


/***/ }),
/* 406 */
/***/ (function(module, exports) {

/* (ignored) */

/***/ }),
/* 407 */
/***/ (function(module, exports, __webpack_require__) {

// Generated by CoffeeScript 1.7.1
(function() {
  var ArrayT, LazyArray, LazyArrayT, NumberT, inspect, utils,
    __hasProp = {}.hasOwnProperty,
    __extends = function(child, parent) { for (var key in parent) { if (__hasProp.call(parent, key)) child[key] = parent[key]; } function ctor() { this.constructor = child; } ctor.prototype = parent.prototype; child.prototype = new ctor(); child.__super__ = parent.prototype; return child; };

  ArrayT = __webpack_require__(252);

  NumberT = __webpack_require__(89).Number;

  utils = __webpack_require__(40);

  inspect = __webpack_require__(166).inspect;

  LazyArrayT = (function(_super) {
    __extends(LazyArrayT, _super);

    function LazyArrayT() {
      return LazyArrayT.__super__.constructor.apply(this, arguments);
    }

    LazyArrayT.prototype.decode = function(stream, parent) {
      var length, pos, res;
      pos = stream.pos;
      length = utils.resolveLength(this.length, stream, parent);
      if (this.length instanceof NumberT) {
        parent = {
          parent: parent,
          _startOffset: pos,
          _currentOffset: 0,
          _length: length
        };
      }
      res = new LazyArray(this.type, length, stream, parent);
      stream.pos += length * this.type.size(null, parent);
      return res;
    };

    LazyArrayT.prototype.size = function(val, ctx) {
      if (val instanceof LazyArray) {
        val = val.toArray();
      }
      return LazyArrayT.__super__.size.call(this, val, ctx);
    };

    LazyArrayT.prototype.encode = function(stream, val, ctx) {
      if (val instanceof LazyArray) {
        val = val.toArray();
      }
      return LazyArrayT.__super__.encode.call(this, stream, val, ctx);
    };

    return LazyArrayT;

  })(ArrayT);

  LazyArray = (function() {
    function LazyArray(type, length, stream, ctx) {
      this.type = type;
      this.length = length;
      this.stream = stream;
      this.ctx = ctx;
      this.base = this.stream.pos;
      this.items = [];
    }

    LazyArray.prototype.get = function(index) {
      var pos;
      if (index < 0 || index >= this.length) {
        return void 0;
      }
      if (this.items[index] == null) {
        pos = this.stream.pos;
        this.stream.pos = this.base + this.type.size(null, this.ctx) * index;
        this.items[index] = this.type.decode(this.stream, this.ctx);
        this.stream.pos = pos;
      }
      return this.items[index];
    };

    LazyArray.prototype.toArray = function() {
      var i, _i, _ref, _results;
      _results = [];
      for (i = _i = 0, _ref = this.length; _i < _ref; i = _i += 1) {
        _results.push(this.get(i));
      }
      return _results;
    };

    LazyArray.prototype.inspect = function() {
      return inspect(this.toArray());
    };

    return LazyArray;

  })();

  module.exports = LazyArrayT;

}).call(this);


/***/ }),
/* 408 */
/***/ (function(module, exports) {

// Generated by CoffeeScript 1.7.1
(function() {
  var Bitfield;

  Bitfield = (function() {
    function Bitfield(type, flags) {
      this.type = type;
      this.flags = flags != null ? flags : [];
    }

    Bitfield.prototype.decode = function(stream) {
      var flag, i, res, val, _i, _len, _ref;
      val = this.type.decode(stream);
      res = {};
      _ref = this.flags;
      for (i = _i = 0, _len = _ref.length; _i < _len; i = ++_i) {
        flag = _ref[i];
        if (flag != null) {
          res[flag] = !!(val & (1 << i));
        }
      }
      return res;
    };

    Bitfield.prototype.size = function() {
      return this.type.size();
    };

    Bitfield.prototype.encode = function(stream, keys) {
      var flag, i, val, _i, _len, _ref;
      val = 0;
      _ref = this.flags;
      for (i = _i = 0, _len = _ref.length; _i < _len; i = ++_i) {
        flag = _ref[i];
        if (flag != null) {
          if (keys[flag]) {
            val |= 1 << i;
          }
        }
      }
      return this.type.encode(stream, val);
    };

    return Bitfield;

  })();

  module.exports = Bitfield;

}).call(this);


/***/ }),
/* 409 */
/***/ (function(module, exports) {

// Generated by CoffeeScript 1.7.1
(function() {
  var BooleanT;

  BooleanT = (function() {
    function BooleanT(type) {
      this.type = type;
    }

    BooleanT.prototype.decode = function(stream, parent) {
      return !!this.type.decode(stream, parent);
    };

    BooleanT.prototype.size = function(val, parent) {
      return this.type.size(val, parent);
    };

    BooleanT.prototype.encode = function(stream, val, parent) {
      return this.type.encode(stream, +val, parent);
    };

    return BooleanT;

  })();

  module.exports = BooleanT;

}).call(this);


/***/ }),
/* 410 */
/***/ (function(module, exports, __webpack_require__) {

// Generated by CoffeeScript 1.7.1
(function() {
  var BufferT, NumberT, utils;

  utils = __webpack_require__(40);

  NumberT = __webpack_require__(89).Number;

  BufferT = (function() {
    function BufferT(length) {
      this.length = length;
    }

    BufferT.prototype.decode = function(stream, parent) {
      var length;
      length = utils.resolveLength(this.length, stream, parent);
      return stream.readBuffer(length);
    };

    BufferT.prototype.size = function(val, parent) {
      if (!val) {
        return utils.resolveLength(this.length, null, parent);
      }
      return val.length;
    };

    BufferT.prototype.encode = function(stream, buf, parent) {
      if (this.length instanceof NumberT) {
        this.length.encode(stream, buf.length);
      }
      return stream.writeBuffer(buf);
    };

    return BufferT;

  })();

  module.exports = BufferT;

}).call(this);


/***/ }),
/* 411 */
/***/ (function(module, exports) {

// Generated by CoffeeScript 1.7.1
(function() {
  var Enum;

  Enum = (function() {
    function Enum(type, options) {
      this.type = type;
      this.options = options != null ? options : [];
    }

    Enum.prototype.decode = function(stream) {
      var index;
      index = this.type.decode(stream);
      return this.options[index] || index;
    };

    Enum.prototype.size = function() {
      return this.type.size();
    };

    Enum.prototype.encode = function(stream, val) {
      var index;
      index = this.options.indexOf(val);
      if (index === -1) {
        throw new Error("Unknown option in enum: " + val);
      }
      return this.type.encode(stream, index);
    };

    return Enum;

  })();

  module.exports = Enum;

}).call(this);


/***/ }),
/* 412 */
/***/ (function(module, exports) {

// Generated by CoffeeScript 1.7.1
(function() {
  var Optional;

  Optional = (function() {
    function Optional(type, condition) {
      this.type = type;
      this.condition = condition != null ? condition : true;
    }

    Optional.prototype.decode = function(stream, parent) {
      var condition;
      condition = this.condition;
      if (typeof condition === 'function') {
        condition = condition.call(parent, parent);
      }
      if (condition) {
        return this.type.decode(stream, parent);
      }
    };

    Optional.prototype.size = function(val, parent) {
      var condition;
      condition = this.condition;
      if (typeof condition === 'function') {
        condition = condition.call(parent, parent);
      }
      if (condition) {
        return this.type.size(val, parent);
      } else {
        return 0;
      }
    };

    Optional.prototype.encode = function(stream, val, parent) {
      var condition;
      condition = this.condition;
      if (typeof condition === 'function') {
        condition = condition.call(parent, parent);
      }
      if (condition) {
        return this.type.encode(stream, val, parent);
      }
    };

    return Optional;

  })();

  module.exports = Optional;

}).call(this);


/***/ }),
/* 413 */
/***/ (function(module, exports, __webpack_require__) {

// Generated by CoffeeScript 1.7.1
(function() {
  var Reserved, utils;

  utils = __webpack_require__(40);

  Reserved = (function() {
    function Reserved(type, count) {
      this.type = type;
      this.count = count != null ? count : 1;
    }

    Reserved.prototype.decode = function(stream, parent) {
      stream.pos += this.size(null, parent);
      return void 0;
    };

    Reserved.prototype.size = function(data, parent) {
      var count;
      count = utils.resolveLength(this.count, null, parent);
      return this.type.size() * count;
    };

    Reserved.prototype.encode = function(stream, val, parent) {
      return stream.fill(0, this.size(val, parent));
    };

    return Reserved;

  })();

  module.exports = Reserved;

}).call(this);


/***/ }),
/* 414 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(Buffer) {// Generated by CoffeeScript 1.7.1
(function() {
  var NumberT, StringT, utils;

  NumberT = __webpack_require__(89).Number;

  utils = __webpack_require__(40);

  StringT = (function() {
    function StringT(length, encoding) {
      this.length = length;
      this.encoding = encoding != null ? encoding : 'ascii';
    }

    StringT.prototype.decode = function(stream, parent) {
      var buffer, encoding, length, pos, string;
      length = (function() {
        if (this.length != null) {
          return utils.resolveLength(this.length, stream, parent);
        } else {
          buffer = stream.buffer, length = stream.length, pos = stream.pos;
          while (pos < length && buffer[pos] !== 0x00) {
            ++pos;
          }
          return pos - stream.pos;
        }
      }).call(this);
      encoding = this.encoding;
      if (typeof encoding === 'function') {
        encoding = encoding.call(parent, parent) || 'ascii';
      }
      string = stream.readString(length, encoding);
      if ((this.length == null) && stream.pos < stream.length) {
        stream.pos++;
      }
      return string;
    };

    StringT.prototype.size = function(val, parent) {
      var encoding, size;
      if (!val) {
        return utils.resolveLength(this.length, null, parent);
      }
      encoding = this.encoding;
      if (typeof encoding === 'function') {
        encoding = encoding.call(parent != null ? parent.val : void 0, parent != null ? parent.val : void 0) || 'ascii';
      }
      if (encoding === 'utf16be') {
        encoding = 'utf16le';
      }
      size = Buffer.byteLength(val, encoding);
      if (this.length instanceof NumberT) {
        size += this.length.size();
      }
      if (this.length == null) {
        size++;
      }
      return size;
    };

    StringT.prototype.encode = function(stream, val, parent) {
      var encoding;
      encoding = this.encoding;
      if (typeof encoding === 'function') {
        encoding = encoding.call(parent != null ? parent.val : void 0, parent != null ? parent.val : void 0) || 'ascii';
      }
      if (this.length instanceof NumberT) {
        this.length.encode(stream, Buffer.byteLength(val, encoding));
      }
      stream.writeString(val, encoding);
      if (this.length == null) {
        return stream.writeUInt8(0x00);
      }
    };

    return StringT;

  })();

  module.exports = StringT;

}).call(this);

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer))

/***/ }),
/* 415 */
/***/ (function(module, exports, __webpack_require__) {

// Generated by CoffeeScript 1.7.1
(function() {
  var Struct, VersionedStruct,
    __hasProp = {}.hasOwnProperty,
    __extends = function(child, parent) { for (var key in parent) { if (__hasProp.call(parent, key)) child[key] = parent[key]; } function ctor() { this.constructor = child; } ctor.prototype = parent.prototype; child.prototype = new ctor(); child.__super__ = parent.prototype; return child; };

  Struct = __webpack_require__(253);

  VersionedStruct = (function(_super) {
    __extends(VersionedStruct, _super);

    function VersionedStruct(type, versions) {
      this.type = type;
      this.versions = versions != null ? versions : {};
      if (typeof this.type === 'string') {
        this.versionGetter = new Function('parent', "return parent." + this.type);
        this.versionSetter = new Function('parent', 'version', "return parent." + this.type + " = version");
      }
    }

    VersionedStruct.prototype.decode = function(stream, parent, length) {
      var fields, res, _ref;
      if (length == null) {
        length = 0;
      }
      res = this._setup(stream, parent, length);
      if (typeof this.type === 'string') {
        res.version = this.versionGetter(parent);
      } else {
        res.version = this.type.decode(stream);
      }
      if (this.versions.header) {
        this._parseFields(stream, res, this.versions.header);
      }
      fields = this.versions[res.version];
      if (fields == null) {
        throw new Error("Unknown version " + res.version);
      }
      if (fields instanceof VersionedStruct) {
        return fields.decode(stream, parent);
      }
      this._parseFields(stream, res, fields);
      if ((_ref = this.process) != null) {
        _ref.call(res, stream);
      }
      return res;
    };

    VersionedStruct.prototype.size = function(val, parent, includePointers) {
      var ctx, fields, key, size, type, _ref;
      if (includePointers == null) {
        includePointers = true;
      }
      if (!val) {
        throw new Error('Not a fixed size');
      }
      ctx = {
        parent: parent,
        val: val,
        pointerSize: 0
      };
      size = 0;
      if (typeof this.type !== 'string') {
        size += this.type.size(val.version, ctx);
      }
      if (this.versions.header) {
        _ref = this.versions.header;
        for (key in _ref) {
          type = _ref[key];
          if (type.size != null) {
            size += type.size(val[key], ctx);
          }
        }
      }
      fields = this.versions[val.version];
      if (fields == null) {
        throw new Error("Unknown version " + val.version);
      }
      for (key in fields) {
        type = fields[key];
        if (type.size != null) {
          size += type.size(val[key], ctx);
        }
      }
      if (includePointers) {
        size += ctx.pointerSize;
      }
      return size;
    };

    VersionedStruct.prototype.encode = function(stream, val, parent) {
      var ctx, fields, i, key, ptr, type, _ref, _ref1;
      if ((_ref = this.preEncode) != null) {
        _ref.call(val, stream);
      }
      ctx = {
        pointers: [],
        startOffset: stream.pos,
        parent: parent,
        val: val,
        pointerSize: 0
      };
      ctx.pointerOffset = stream.pos + this.size(val, ctx, false);
      if (typeof this.type !== 'string') {
        this.type.encode(stream, val.version);
      }
      if (this.versions.header) {
        _ref1 = this.versions.header;
        for (key in _ref1) {
          type = _ref1[key];
          if (type.encode != null) {
            type.encode(stream, val[key], ctx);
          }
        }
      }
      fields = this.versions[val.version];
      for (key in fields) {
        type = fields[key];
        if (type.encode != null) {
          type.encode(stream, val[key], ctx);
        }
      }
      i = 0;
      while (i < ctx.pointers.length) {
        ptr = ctx.pointers[i++];
        ptr.type.encode(stream, ptr.val, ptr.parent);
      }
    };

    return VersionedStruct;

  })(Struct);

  module.exports = VersionedStruct;

}).call(this);


/***/ }),
/* 416 */
/***/ (function(module, exports, __webpack_require__) {

// Generated by CoffeeScript 1.7.1
(function() {
  var Pointer, VoidPointer, utils;

  utils = __webpack_require__(40);

  Pointer = (function() {
    function Pointer(offsetType, type, options) {
      var _base, _base1, _base2, _base3;
      this.offsetType = offsetType;
      this.type = type;
      this.options = options != null ? options : {};
      if (this.type === 'void') {
        this.type = null;
      }
      if ((_base = this.options).type == null) {
        _base.type = 'local';
      }
      if ((_base1 = this.options).allowNull == null) {
        _base1.allowNull = true;
      }
      if ((_base2 = this.options).nullValue == null) {
        _base2.nullValue = 0;
      }
      if ((_base3 = this.options).lazy == null) {
        _base3.lazy = false;
      }
      if (this.options.relativeTo) {
        this.relativeToGetter = new Function('ctx', "return ctx." + this.options.relativeTo);
      }
    }

    Pointer.prototype.decode = function(stream, ctx) {
      var c, decodeValue, offset, ptr, relative, val;
      offset = this.offsetType.decode(stream, ctx);
      if (offset === this.options.nullValue && this.options.allowNull) {
        return null;
      }
      relative = (function() {
        switch (this.options.type) {
          case 'local':
            return ctx._startOffset;
          case 'immediate':
            return stream.pos - this.offsetType.size();
          case 'parent':
            return ctx.parent._startOffset;
          default:
            c = ctx;
            while (c.parent) {
              c = c.parent;
            }
            return c._startOffset || 0;
        }
      }).call(this);
      if (this.options.relativeTo) {
        relative += this.relativeToGetter(ctx);
      }
      ptr = offset + relative;
      if (this.type != null) {
        val = null;
        decodeValue = (function(_this) {
          return function() {
            var pos;
            if (val != null) {
              return val;
            }
            pos = stream.pos;
            stream.pos = ptr;
            val = _this.type.decode(stream, ctx);
            stream.pos = pos;
            return val;
          };
        })(this);
        if (this.options.lazy) {
          return new utils.PropertyDescriptor({
            get: decodeValue
          });
        }
        return decodeValue();
      } else {
        return ptr;
      }
    };

    Pointer.prototype.size = function(val, ctx) {
      var parent, type;
      parent = ctx;
      switch (this.options.type) {
        case 'local':
        case 'immediate':
          break;
        case 'parent':
          ctx = ctx.parent;
          break;
        default:
          while (ctx.parent) {
            ctx = ctx.parent;
          }
      }
      type = this.type;
      if (type == null) {
        if (!(val instanceof VoidPointer)) {
          throw new Error("Must be a VoidPointer");
        }
        type = val.type;
        val = val.value;
      }
      if (val && ctx) {
        ctx.pointerSize += type.size(val, parent);
      }
      return this.offsetType.size();
    };

    Pointer.prototype.encode = function(stream, val, ctx) {
      var parent, relative, type;
      parent = ctx;
      if (val == null) {
        this.offsetType.encode(stream, this.options.nullValue);
        return;
      }
      switch (this.options.type) {
        case 'local':
          relative = ctx.startOffset;
          break;
        case 'immediate':
          relative = stream.pos + this.offsetType.size(val, parent);
          break;
        case 'parent':
          ctx = ctx.parent;
          relative = ctx.startOffset;
          break;
        default:
          relative = 0;
          while (ctx.parent) {
            ctx = ctx.parent;
          }
      }
      if (this.options.relativeTo) {
        relative += this.relativeToGetter(parent.val);
      }
      this.offsetType.encode(stream, ctx.pointerOffset - relative);
      type = this.type;
      if (type == null) {
        if (!(val instanceof VoidPointer)) {
          throw new Error("Must be a VoidPointer");
        }
        type = val.type;
        val = val.value;
      }
      ctx.pointers.push({
        type: type,
        val: val,
        parent: parent
      });
      return ctx.pointerOffset += type.size(val, parent);
    };

    return Pointer;

  })();

  VoidPointer = (function() {
    function VoidPointer(type, value) {
      this.type = type;
      this.value = value;
    }

    return VoidPointer;

  })();

  exports.Pointer = Pointer;

  exports.VoidPointer = VoidPointer;

}).call(this);


/***/ }),
/* 417 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(418), __esModule: true };

/***/ }),
/* 418 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(419);
var $Object = __webpack_require__(7).Object;
module.exports = function getOwnPropertyDescriptor(it, key) {
  return $Object.getOwnPropertyDescriptor(it, key);
};


/***/ }),
/* 419 */
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.6 Object.getOwnPropertyDescriptor(O, P)
var toIObject = __webpack_require__(50);
var $getOwnPropertyDescriptor = __webpack_require__(174).f;

__webpack_require__(177)('getOwnPropertyDescriptor', function () {
  return function getOwnPropertyDescriptor(it, key) {
    return $getOwnPropertyDescriptor(toIObject(it), key);
  };
});


/***/ }),
/* 420 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(421), __esModule: true };

/***/ }),
/* 421 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(102);
__webpack_require__(91);
module.exports = __webpack_require__(428);


/***/ }),
/* 422 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var addToUnscopables = __webpack_require__(423);
var step = __webpack_require__(255);
var Iterators = __webpack_require__(103);
var toIObject = __webpack_require__(50);

// 22.1.3.4 Array.prototype.entries()
// 22.1.3.13 Array.prototype.keys()
// 22.1.3.29 Array.prototype.values()
// 22.1.3.30 Array.prototype[@@iterator]()
module.exports = __webpack_require__(178)(Array, 'Array', function (iterated, kind) {
  this._t = toIObject(iterated); // target
  this._i = 0;                   // next index
  this._k = kind;                // kind
// 22.1.5.2.1 %ArrayIteratorPrototype%.next()
}, function () {
  var O = this._t;
  var kind = this._k;
  var index = this._i++;
  if (!O || index >= O.length) {
    this._t = undefined;
    return step(1);
  }
  if (kind == 'keys') return step(0, index);
  if (kind == 'values') return step(0, O[index]);
  return step(0, [index, O[index]]);
}, 'values');

// argumentsList[@@iterator] is %ArrayProto_values% (9.4.4.6, 9.4.4.7)
Iterators.Arguments = Iterators.Array;

addToUnscopables('keys');
addToUnscopables('values');
addToUnscopables('entries');


/***/ }),
/* 423 */
/***/ (function(module, exports) {

module.exports = function () { /* empty */ };


/***/ }),
/* 424 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var create = __webpack_require__(129);
var descriptor = __webpack_require__(100);
var setToStringTag = __webpack_require__(106);
var IteratorPrototype = {};

// 25.1.2.1.1 %IteratorPrototype%[@@iterator]()
__webpack_require__(41)(IteratorPrototype, __webpack_require__(21)('iterator'), function () { return this; });

module.exports = function (Constructor, NAME, next) {
  Constructor.prototype = create(IteratorPrototype, { next: descriptor(1, next) });
  setToStringTag(Constructor, NAME + ' Iterator');
};


/***/ }),
/* 425 */
/***/ (function(module, exports, __webpack_require__) {

// false -> Array#indexOf
// true  -> Array#includes
var toIObject = __webpack_require__(50);
var toLength = __webpack_require__(130);
var toAbsoluteIndex = __webpack_require__(259);
module.exports = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIObject($this);
    var length = toLength(O.length);
    var index = toAbsoluteIndex(fromIndex, length);
    var value;
    // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare
    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++];
      // eslint-disable-next-line no-self-compare
      if (value != value) return true;
    // Array#indexOf ignores holes, Array#includes - not
    } else for (;length > index; index++) if (IS_INCLUDES || index in O) {
      if (O[index] === el) return IS_INCLUDES || index || 0;
    } return !IS_INCLUDES && -1;
  };
};


/***/ }),
/* 426 */
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.9 / 15.2.3.2 Object.getPrototypeOf(O)
var has = __webpack_require__(51);
var toObject = __webpack_require__(90);
var IE_PROTO = __webpack_require__(180)('IE_PROTO');
var ObjectProto = Object.prototype;

module.exports = Object.getPrototypeOf || function (O) {
  O = toObject(O);
  if (has(O, IE_PROTO)) return O[IE_PROTO];
  if (typeof O.constructor == 'function' && O instanceof O.constructor) {
    return O.constructor.prototype;
  } return O instanceof Object ? ObjectProto : null;
};


/***/ }),
/* 427 */
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__(179);
var defined = __webpack_require__(173);
// true  -> String#at
// false -> String#codePointAt
module.exports = function (TO_STRING) {
  return function (that, pos) {
    var s = String(defined(that));
    var i = toInteger(pos);
    var l = s.length;
    var a, b;
    if (i < 0 || i >= l) return TO_STRING ? '' : undefined;
    a = s.charCodeAt(i);
    return a < 0xd800 || a > 0xdbff || i + 1 === l || (b = s.charCodeAt(i + 1)) < 0xdc00 || b > 0xdfff
      ? TO_STRING ? s.charAt(i) : a
      : TO_STRING ? s.slice(i, i + 2) : (a - 0xd800 << 10) + (b - 0xdc00) + 0x10000;
  };
};


/***/ }),
/* 428 */
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__(29);
var get = __webpack_require__(183);
module.exports = __webpack_require__(7).getIterator = function (it) {
  var iterFn = get(it);
  if (typeof iterFn != 'function') throw TypeError(it + ' is not iterable!');
  return anObject(iterFn.call(it));
};


/***/ }),
/* 429 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(430), __esModule: true };

/***/ }),
/* 430 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(431);
module.exports = __webpack_require__(7).Object.freeze;


/***/ }),
/* 431 */
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.5 Object.freeze(O)
var isObject = __webpack_require__(22);
var meta = __webpack_require__(132).onFreeze;

__webpack_require__(177)('freeze', function ($freeze) {
  return function freeze(it) {
    return $freeze && isObject(it) ? $freeze(meta(it)) : it;
  };
});


/***/ }),
/* 432 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(433), __esModule: true };

/***/ }),
/* 433 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(91);
__webpack_require__(102);
module.exports = __webpack_require__(186).f('iterator');


/***/ }),
/* 434 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(435), __esModule: true };

/***/ }),
/* 435 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(436);
__webpack_require__(133);
__webpack_require__(439);
__webpack_require__(440);
module.exports = __webpack_require__(7).Symbol;


/***/ }),
/* 436 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

// ECMAScript 6 symbols shim
var global = __webpack_require__(17);
var has = __webpack_require__(51);
var DESCRIPTORS = __webpack_require__(23);
var $export = __webpack_require__(9);
var redefine = __webpack_require__(256);
var META = __webpack_require__(132).KEY;
var $fails = __webpack_require__(52);
var shared = __webpack_require__(181);
var setToStringTag = __webpack_require__(106);
var uid = __webpack_require__(131);
var wks = __webpack_require__(21);
var wksExt = __webpack_require__(186);
var wksDefine = __webpack_require__(187);
var enumKeys = __webpack_require__(437);
var isArray = __webpack_require__(261);
var anObject = __webpack_require__(29);
var isObject = __webpack_require__(22);
var toObject = __webpack_require__(90);
var toIObject = __webpack_require__(50);
var toPrimitive = __webpack_require__(175);
var createDesc = __webpack_require__(100);
var _create = __webpack_require__(129);
var gOPNExt = __webpack_require__(438);
var $GOPD = __webpack_require__(174);
var $GOPS = __webpack_require__(188);
var $DP = __webpack_require__(26);
var $keys = __webpack_require__(105);
var gOPD = $GOPD.f;
var dP = $DP.f;
var gOPN = gOPNExt.f;
var $Symbol = global.Symbol;
var $JSON = global.JSON;
var _stringify = $JSON && $JSON.stringify;
var PROTOTYPE = 'prototype';
var HIDDEN = wks('_hidden');
var TO_PRIMITIVE = wks('toPrimitive');
var isEnum = {}.propertyIsEnumerable;
var SymbolRegistry = shared('symbol-registry');
var AllSymbols = shared('symbols');
var OPSymbols = shared('op-symbols');
var ObjectProto = Object[PROTOTYPE];
var USE_NATIVE = typeof $Symbol == 'function' && !!$GOPS.f;
var QObject = global.QObject;
// Don't use setters in Qt Script, https://github.com/zloirock/core-js/issues/173
var setter = !QObject || !QObject[PROTOTYPE] || !QObject[PROTOTYPE].findChild;

// fallback for old Android, https://code.google.com/p/v8/issues/detail?id=687
var setSymbolDesc = DESCRIPTORS && $fails(function () {
  return _create(dP({}, 'a', {
    get: function () { return dP(this, 'a', { value: 7 }).a; }
  })).a != 7;
}) ? function (it, key, D) {
  var protoDesc = gOPD(ObjectProto, key);
  if (protoDesc) delete ObjectProto[key];
  dP(it, key, D);
  if (protoDesc && it !== ObjectProto) dP(ObjectProto, key, protoDesc);
} : dP;

var wrap = function (tag) {
  var sym = AllSymbols[tag] = _create($Symbol[PROTOTYPE]);
  sym._k = tag;
  return sym;
};

var isSymbol = USE_NATIVE && typeof $Symbol.iterator == 'symbol' ? function (it) {
  return typeof it == 'symbol';
} : function (it) {
  return it instanceof $Symbol;
};

var $defineProperty = function defineProperty(it, key, D) {
  if (it === ObjectProto) $defineProperty(OPSymbols, key, D);
  anObject(it);
  key = toPrimitive(key, true);
  anObject(D);
  if (has(AllSymbols, key)) {
    if (!D.enumerable) {
      if (!has(it, HIDDEN)) dP(it, HIDDEN, createDesc(1, {}));
      it[HIDDEN][key] = true;
    } else {
      if (has(it, HIDDEN) && it[HIDDEN][key]) it[HIDDEN][key] = false;
      D = _create(D, { enumerable: createDesc(0, false) });
    } return setSymbolDesc(it, key, D);
  } return dP(it, key, D);
};
var $defineProperties = function defineProperties(it, P) {
  anObject(it);
  var keys = enumKeys(P = toIObject(P));
  var i = 0;
  var l = keys.length;
  var key;
  while (l > i) $defineProperty(it, key = keys[i++], P[key]);
  return it;
};
var $create = function create(it, P) {
  return P === undefined ? _create(it) : $defineProperties(_create(it), P);
};
var $propertyIsEnumerable = function propertyIsEnumerable(key) {
  var E = isEnum.call(this, key = toPrimitive(key, true));
  if (this === ObjectProto && has(AllSymbols, key) && !has(OPSymbols, key)) return false;
  return E || !has(this, key) || !has(AllSymbols, key) || has(this, HIDDEN) && this[HIDDEN][key] ? E : true;
};
var $getOwnPropertyDescriptor = function getOwnPropertyDescriptor(it, key) {
  it = toIObject(it);
  key = toPrimitive(key, true);
  if (it === ObjectProto && has(AllSymbols, key) && !has(OPSymbols, key)) return;
  var D = gOPD(it, key);
  if (D && has(AllSymbols, key) && !(has(it, HIDDEN) && it[HIDDEN][key])) D.enumerable = true;
  return D;
};
var $getOwnPropertyNames = function getOwnPropertyNames(it) {
  var names = gOPN(toIObject(it));
  var result = [];
  var i = 0;
  var key;
  while (names.length > i) {
    if (!has(AllSymbols, key = names[i++]) && key != HIDDEN && key != META) result.push(key);
  } return result;
};
var $getOwnPropertySymbols = function getOwnPropertySymbols(it) {
  var IS_OP = it === ObjectProto;
  var names = gOPN(IS_OP ? OPSymbols : toIObject(it));
  var result = [];
  var i = 0;
  var key;
  while (names.length > i) {
    if (has(AllSymbols, key = names[i++]) && (IS_OP ? has(ObjectProto, key) : true)) result.push(AllSymbols[key]);
  } return result;
};

// 19.4.1.1 Symbol([description])
if (!USE_NATIVE) {
  $Symbol = function Symbol() {
    if (this instanceof $Symbol) throw TypeError('Symbol is not a constructor!');
    var tag = uid(arguments.length > 0 ? arguments[0] : undefined);
    var $set = function (value) {
      if (this === ObjectProto) $set.call(OPSymbols, value);
      if (has(this, HIDDEN) && has(this[HIDDEN], tag)) this[HIDDEN][tag] = false;
      setSymbolDesc(this, tag, createDesc(1, value));
    };
    if (DESCRIPTORS && setter) setSymbolDesc(ObjectProto, tag, { configurable: true, set: $set });
    return wrap(tag);
  };
  redefine($Symbol[PROTOTYPE], 'toString', function toString() {
    return this._k;
  });

  $GOPD.f = $getOwnPropertyDescriptor;
  $DP.f = $defineProperty;
  __webpack_require__(262).f = gOPNExt.f = $getOwnPropertyNames;
  __webpack_require__(128).f = $propertyIsEnumerable;
  $GOPS.f = $getOwnPropertySymbols;

  if (DESCRIPTORS && !__webpack_require__(104)) {
    redefine(ObjectProto, 'propertyIsEnumerable', $propertyIsEnumerable, true);
  }

  wksExt.f = function (name) {
    return wrap(wks(name));
  };
}

$export($export.G + $export.W + $export.F * !USE_NATIVE, { Symbol: $Symbol });

for (var es6Symbols = (
  // 19.4.2.2, 19.4.2.3, 19.4.2.4, 19.4.2.6, 19.4.2.8, 19.4.2.9, 19.4.2.10, 19.4.2.11, 19.4.2.12, 19.4.2.13, 19.4.2.14
  'hasInstance,isConcatSpreadable,iterator,match,replace,search,species,split,toPrimitive,toStringTag,unscopables'
).split(','), j = 0; es6Symbols.length > j;)wks(es6Symbols[j++]);

for (var wellKnownSymbols = $keys(wks.store), k = 0; wellKnownSymbols.length > k;) wksDefine(wellKnownSymbols[k++]);

$export($export.S + $export.F * !USE_NATIVE, 'Symbol', {
  // 19.4.2.1 Symbol.for(key)
  'for': function (key) {
    return has(SymbolRegistry, key += '')
      ? SymbolRegistry[key]
      : SymbolRegistry[key] = $Symbol(key);
  },
  // 19.4.2.5 Symbol.keyFor(sym)
  keyFor: function keyFor(sym) {
    if (!isSymbol(sym)) throw TypeError(sym + ' is not a symbol!');
    for (var key in SymbolRegistry) if (SymbolRegistry[key] === sym) return key;
  },
  useSetter: function () { setter = true; },
  useSimple: function () { setter = false; }
});

$export($export.S + $export.F * !USE_NATIVE, 'Object', {
  // 19.1.2.2 Object.create(O [, Properties])
  create: $create,
  // 19.1.2.4 Object.defineProperty(O, P, Attributes)
  defineProperty: $defineProperty,
  // 19.1.2.3 Object.defineProperties(O, Properties)
  defineProperties: $defineProperties,
  // 19.1.2.6 Object.getOwnPropertyDescriptor(O, P)
  getOwnPropertyDescriptor: $getOwnPropertyDescriptor,
  // 19.1.2.7 Object.getOwnPropertyNames(O)
  getOwnPropertyNames: $getOwnPropertyNames,
  // 19.1.2.8 Object.getOwnPropertySymbols(O)
  getOwnPropertySymbols: $getOwnPropertySymbols
});

// Chrome 38 and 39 `Object.getOwnPropertySymbols` fails on primitives
// https://bugs.chromium.org/p/v8/issues/detail?id=3443
var FAILS_ON_PRIMITIVES = $fails(function () { $GOPS.f(1); });

$export($export.S + $export.F * FAILS_ON_PRIMITIVES, 'Object', {
  getOwnPropertySymbols: function getOwnPropertySymbols(it) {
    return $GOPS.f(toObject(it));
  }
});

// 24.3.2 JSON.stringify(value [, replacer [, space]])
$JSON && $export($export.S + $export.F * (!USE_NATIVE || $fails(function () {
  var S = $Symbol();
  // MS Edge converts symbol values to JSON as {}
  // WebKit converts symbol values to JSON as null
  // V8 throws on boxed symbols
  return _stringify([S]) != '[null]' || _stringify({ a: S }) != '{}' || _stringify(Object(S)) != '{}';
})), 'JSON', {
  stringify: function stringify(it) {
    var args = [it];
    var i = 1;
    var replacer, $replacer;
    while (arguments.length > i) args.push(arguments[i++]);
    $replacer = replacer = args[1];
    if (!isObject(replacer) && it === undefined || isSymbol(it)) return; // IE8 returns string on undefined
    if (!isArray(replacer)) replacer = function (key, value) {
      if (typeof $replacer == 'function') value = $replacer.call(this, key, value);
      if (!isSymbol(value)) return value;
    };
    args[1] = replacer;
    return _stringify.apply($JSON, args);
  }
});

// 19.4.3.4 Symbol.prototype[@@toPrimitive](hint)
$Symbol[PROTOTYPE][TO_PRIMITIVE] || __webpack_require__(41)($Symbol[PROTOTYPE], TO_PRIMITIVE, $Symbol[PROTOTYPE].valueOf);
// 19.4.3.5 Symbol.prototype[@@toStringTag]
setToStringTag($Symbol, 'Symbol');
// 20.2.1.9 Math[@@toStringTag]
setToStringTag(Math, 'Math', true);
// 24.3.3 JSON[@@toStringTag]
setToStringTag(global.JSON, 'JSON', true);


/***/ }),
/* 437 */
/***/ (function(module, exports, __webpack_require__) {

// all enumerable object keys, includes symbols
var getKeys = __webpack_require__(105);
var gOPS = __webpack_require__(188);
var pIE = __webpack_require__(128);
module.exports = function (it) {
  var result = getKeys(it);
  var getSymbols = gOPS.f;
  if (getSymbols) {
    var symbols = getSymbols(it);
    var isEnum = pIE.f;
    var i = 0;
    var key;
    while (symbols.length > i) if (isEnum.call(it, key = symbols[i++])) result.push(key);
  } return result;
};


/***/ }),
/* 438 */
/***/ (function(module, exports, __webpack_require__) {

// fallback for IE11 buggy Object.getOwnPropertyNames with iframe and window
var toIObject = __webpack_require__(50);
var gOPN = __webpack_require__(262).f;
var toString = {}.toString;

var windowNames = typeof window == 'object' && window && Object.getOwnPropertyNames
  ? Object.getOwnPropertyNames(window) : [];

var getWindowNames = function (it) {
  try {
    return gOPN(it);
  } catch (e) {
    return windowNames.slice();
  }
};

module.exports.f = function getOwnPropertyNames(it) {
  return windowNames && toString.call(it) == '[object Window]' ? getWindowNames(it) : gOPN(toIObject(it));
};


/***/ }),
/* 439 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(187)('asyncIterator');


/***/ }),
/* 440 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(187)('observable');


/***/ }),
/* 441 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(442), __esModule: true };

/***/ }),
/* 442 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(443);
module.exports = __webpack_require__(7).Object.keys;


/***/ }),
/* 443 */
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.14 Object.keys(O)
var toObject = __webpack_require__(90);
var $keys = __webpack_require__(105);

__webpack_require__(177)('keys', function () {
  return function keys(it) {
    return $keys(toObject(it));
  };
});


/***/ }),
/* 444 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(445);
var $Object = __webpack_require__(7).Object;
module.exports = function defineProperty(it, key, desc) {
  return $Object.defineProperty(it, key, desc);
};


/***/ }),
/* 445 */
/***/ (function(module, exports, __webpack_require__) {

var $export = __webpack_require__(9);
// 19.1.2.4 / 15.2.3.6 Object.defineProperty(O, P, Attributes)
$export($export.S + $export.F * !__webpack_require__(23), 'Object', { defineProperty: __webpack_require__(26).f });


/***/ }),
/* 446 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


exports.__esModule = true;

exports.default = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

/***/ }),
/* 447 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


exports.__esModule = true;

var _defineProperty = __webpack_require__(263);

var _defineProperty2 = _interopRequireDefault(_defineProperty);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

exports.default = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      (0, _defineProperty2.default)(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

/***/ }),
/* 448 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(449), __esModule: true };

/***/ }),
/* 449 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(133);
__webpack_require__(91);
__webpack_require__(102);
__webpack_require__(450);
__webpack_require__(454);
__webpack_require__(456);
__webpack_require__(457);
module.exports = __webpack_require__(7).Map;


/***/ }),
/* 450 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var strong = __webpack_require__(264);
var validate = __webpack_require__(191);
var MAP = 'Map';

// 23.1 Map Objects
module.exports = __webpack_require__(268)(MAP, function (get) {
  return function Map() { return get(this, arguments.length > 0 ? arguments[0] : undefined); };
}, {
  // 23.1.3.6 Map.prototype.get(key)
  get: function get(key) {
    var entry = strong.getEntry(validate(this, MAP), key);
    return entry && entry.v;
  },
  // 23.1.3.9 Map.prototype.set(key, value)
  set: function set(key, value) {
    return strong.def(validate(this, MAP), key === 0 ? 0 : key, value);
  }
}, strong, true);


/***/ }),
/* 451 */
/***/ (function(module, exports, __webpack_require__) {

// 0 -> Array#forEach
// 1 -> Array#map
// 2 -> Array#filter
// 3 -> Array#some
// 4 -> Array#every
// 5 -> Array#find
// 6 -> Array#findIndex
var ctx = __webpack_require__(33);
var IObject = __webpack_require__(172);
var toObject = __webpack_require__(90);
var toLength = __webpack_require__(130);
var asc = __webpack_require__(452);
module.exports = function (TYPE, $create) {
  var IS_MAP = TYPE == 1;
  var IS_FILTER = TYPE == 2;
  var IS_SOME = TYPE == 3;
  var IS_EVERY = TYPE == 4;
  var IS_FIND_INDEX = TYPE == 6;
  var NO_HOLES = TYPE == 5 || IS_FIND_INDEX;
  var create = $create || asc;
  return function ($this, callbackfn, that) {
    var O = toObject($this);
    var self = IObject(O);
    var f = ctx(callbackfn, that, 3);
    var length = toLength(self.length);
    var index = 0;
    var result = IS_MAP ? create($this, length) : IS_FILTER ? create($this, 0) : undefined;
    var val, res;
    for (;length > index; index++) if (NO_HOLES || index in self) {
      val = self[index];
      res = f(val, index, O);
      if (TYPE) {
        if (IS_MAP) result[index] = res;   // map
        else if (res) switch (TYPE) {
          case 3: return true;             // some
          case 5: return val;              // find
          case 6: return index;            // findIndex
          case 2: result.push(val);        // filter
        } else if (IS_EVERY) return false; // every
      }
    }
    return IS_FIND_INDEX ? -1 : IS_SOME || IS_EVERY ? IS_EVERY : result;
  };
};


/***/ }),
/* 452 */
/***/ (function(module, exports, __webpack_require__) {

// 9.4.2.3 ArraySpeciesCreate(originalArray, length)
var speciesConstructor = __webpack_require__(453);

module.exports = function (original, length) {
  return new (speciesConstructor(original))(length);
};


/***/ }),
/* 453 */
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__(22);
var isArray = __webpack_require__(261);
var SPECIES = __webpack_require__(21)('species');

module.exports = function (original) {
  var C;
  if (isArray(original)) {
    C = original.constructor;
    // cross-realm fallback
    if (typeof C == 'function' && (C === Array || isArray(C.prototype))) C = undefined;
    if (isObject(C)) {
      C = C[SPECIES];
      if (C === null) C = undefined;
    }
  } return C === undefined ? Array : C;
};


/***/ }),
/* 454 */
/***/ (function(module, exports, __webpack_require__) {

// https://github.com/DavidBruant/Map-Set.prototype.toJSON
var $export = __webpack_require__(9);

$export($export.P + $export.R, 'Map', { toJSON: __webpack_require__(269)('Map') });


/***/ }),
/* 455 */
/***/ (function(module, exports, __webpack_require__) {

var forOf = __webpack_require__(107);

module.exports = function (iter, ITERATOR) {
  var result = [];
  forOf(iter, false, result.push, result, ITERATOR);
  return result;
};


/***/ }),
/* 456 */
/***/ (function(module, exports, __webpack_require__) {

// https://tc39.github.io/proposal-setmap-offrom/#sec-map.of
__webpack_require__(270)('Map');


/***/ }),
/* 457 */
/***/ (function(module, exports, __webpack_require__) {

// https://tc39.github.io/proposal-setmap-offrom/#sec-map.from
__webpack_require__(271)('Map');


/***/ }),
/* 458 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


exports.__esModule = true;

var _typeof2 = __webpack_require__(185);

var _typeof3 = _interopRequireDefault(_typeof2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

exports.default = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && ((typeof call === "undefined" ? "undefined" : (0, _typeof3.default)(call)) === "object" || typeof call === "function") ? call : self;
};

/***/ }),
/* 459 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


exports.__esModule = true;

var _setPrototypeOf = __webpack_require__(460);

var _setPrototypeOf2 = _interopRequireDefault(_setPrototypeOf);

var _create = __webpack_require__(464);

var _create2 = _interopRequireDefault(_create);

var _typeof2 = __webpack_require__(185);

var _typeof3 = _interopRequireDefault(_typeof2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

exports.default = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + (typeof superClass === "undefined" ? "undefined" : (0, _typeof3.default)(superClass)));
  }

  subClass.prototype = (0, _create2.default)(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf2.default ? (0, _setPrototypeOf2.default)(subClass, superClass) : subClass.__proto__ = superClass;
};

/***/ }),
/* 460 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(461), __esModule: true };

/***/ }),
/* 461 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(462);
module.exports = __webpack_require__(7).Object.setPrototypeOf;


/***/ }),
/* 462 */
/***/ (function(module, exports, __webpack_require__) {

// 19.1.3.19 Object.setPrototypeOf(O, proto)
var $export = __webpack_require__(9);
$export($export.S, 'Object', { setPrototypeOf: __webpack_require__(463).set });


/***/ }),
/* 463 */
/***/ (function(module, exports, __webpack_require__) {

// Works with __proto__ only. Old v8 can't work with null proto objects.
/* eslint-disable no-proto */
var isObject = __webpack_require__(22);
var anObject = __webpack_require__(29);
var check = function (O, proto) {
  anObject(O);
  if (!isObject(proto) && proto !== null) throw TypeError(proto + ": can't set as prototype!");
};
module.exports = {
  set: Object.setPrototypeOf || ('__proto__' in {} ? // eslint-disable-line
    function (test, buggy, set) {
      try {
        set = __webpack_require__(33)(Function.call, __webpack_require__(174).f(Object.prototype, '__proto__').set, 2);
        set(test, []);
        buggy = !(test instanceof Array);
      } catch (e) { buggy = true; }
      return function setPrototypeOf(O, proto) {
        check(O, proto);
        if (buggy) O.__proto__ = proto;
        else set(O, proto);
        return O;
      };
    }({}, false) : undefined),
  check: check
};


/***/ }),
/* 464 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(465), __esModule: true };

/***/ }),
/* 465 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(466);
var $Object = __webpack_require__(7).Object;
module.exports = function create(P, D) {
  return $Object.create(P, D);
};


/***/ }),
/* 466 */
/***/ (function(module, exports, __webpack_require__) {

var $export = __webpack_require__(9);
// 19.1.2.2 / 15.2.3.5 Object.create(O [, Properties])
$export($export.S, 'Object', { create: __webpack_require__(129) });


/***/ }),
/* 467 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(468), __esModule: true };

/***/ }),
/* 468 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(469);
var $Object = __webpack_require__(7).Object;
module.exports = function defineProperties(T, D) {
  return $Object.defineProperties(T, D);
};


/***/ }),
/* 469 */
/***/ (function(module, exports, __webpack_require__) {

var $export = __webpack_require__(9);
// 19.1.2.3 / 15.2.3.7 Object.defineProperties(O, Properties)
$export($export.S + $export.F * !__webpack_require__(23), 'Object', { defineProperties: __webpack_require__(257) });


/***/ }),
/* 470 */
/***/ (function(module, exports, __webpack_require__) {

var objectKeys = __webpack_require__(272);
var isArguments = __webpack_require__(472);
var is = __webpack_require__(473);
var isRegex = __webpack_require__(478);
var flags = __webpack_require__(479);
var isDate = __webpack_require__(481);

var getTime = Date.prototype.getTime;

function deepEqual(actual, expected, options) {
  var opts = options || {};

  // 7.1. All identical values are equivalent, as determined by ===.
  if (opts.strict ? is(actual, expected) : actual === expected) {
    return true;
  }

  // 7.3. Other pairs that do not both pass typeof value == 'object', equivalence is determined by ==.
  if (!actual || !expected || (typeof actual !== 'object' && typeof expected !== 'object')) {
    return opts.strict ? is(actual, expected) : actual == expected;
  }

  /*
   * 7.4. For all other Object pairs, including Array objects, equivalence is
   * determined by having the same number of owned properties (as verified
   * with Object.prototype.hasOwnProperty.call), the same set of keys
   * (although not necessarily the same order), equivalent values for every
   * corresponding key, and an identical 'prototype' property. Note: this
   * accounts for both named and indexed properties on Arrays.
   */
  // eslint-disable-next-line no-use-before-define
  return objEquiv(actual, expected, opts);
}

function isUndefinedOrNull(value) {
  return value === null || value === undefined;
}

function isBuffer(x) {
  if (!x || typeof x !== 'object' || typeof x.length !== 'number') {
    return false;
  }
  if (typeof x.copy !== 'function' || typeof x.slice !== 'function') {
    return false;
  }
  if (x.length > 0 && typeof x[0] !== 'number') {
    return false;
  }
  return true;
}

function objEquiv(a, b, opts) {
  /* eslint max-statements: [2, 50] */
  var i, key;
  if (typeof a !== typeof b) { return false; }
  if (isUndefinedOrNull(a) || isUndefinedOrNull(b)) { return false; }

  // an identical 'prototype' property.
  if (a.prototype !== b.prototype) { return false; }

  if (isArguments(a) !== isArguments(b)) { return false; }

  var aIsRegex = isRegex(a);
  var bIsRegex = isRegex(b);
  if (aIsRegex !== bIsRegex) { return false; }
  if (aIsRegex || bIsRegex) {
    return a.source === b.source && flags(a) === flags(b);
  }

  if (isDate(a) && isDate(b)) {
    return getTime.call(a) === getTime.call(b);
  }

  var aIsBuffer = isBuffer(a);
  var bIsBuffer = isBuffer(b);
  if (aIsBuffer !== bIsBuffer) { return false; }
  if (aIsBuffer || bIsBuffer) { // && would work too, because both are true or both false here
    if (a.length !== b.length) { return false; }
    for (i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) { return false; }
    }
    return true;
  }

  if (typeof a !== typeof b) { return false; }

  try {
    var ka = objectKeys(a);
    var kb = objectKeys(b);
  } catch (e) { // happens when one is a string literal and the other isn't
    return false;
  }
  // having the same number of owned properties (keys incorporates hasOwnProperty)
  if (ka.length !== kb.length) { return false; }

  // the same set of keys (although not necessarily the same order),
  ka.sort();
  kb.sort();
  // ~~~cheap key test
  for (i = ka.length - 1; i >= 0; i--) {
    if (ka[i] != kb[i]) { return false; }
  }
  // equivalent values for every corresponding key, and ~~~possibly expensive deep test
  for (i = ka.length - 1; i >= 0; i--) {
    key = ka[i];
    if (!deepEqual(a[key], b[key], opts)) { return false; }
  }

  return true;
}

module.exports = deepEqual;


/***/ }),
/* 471 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var keysShim;
if (!Object.keys) {
	// modified from https://github.com/es-shims/es5-shim
	var has = Object.prototype.hasOwnProperty;
	var toStr = Object.prototype.toString;
	var isArgs = __webpack_require__(273); // eslint-disable-line global-require
	var isEnumerable = Object.prototype.propertyIsEnumerable;
	var hasDontEnumBug = !isEnumerable.call({ toString: null }, 'toString');
	var hasProtoEnumBug = isEnumerable.call(function () {}, 'prototype');
	var dontEnums = [
		'toString',
		'toLocaleString',
		'valueOf',
		'hasOwnProperty',
		'isPrototypeOf',
		'propertyIsEnumerable',
		'constructor'
	];
	var equalsConstructorPrototype = function (o) {
		var ctor = o.constructor;
		return ctor && ctor.prototype === o;
	};
	var excludedKeys = {
		$applicationCache: true,
		$console: true,
		$external: true,
		$frame: true,
		$frameElement: true,
		$frames: true,
		$innerHeight: true,
		$innerWidth: true,
		$onmozfullscreenchange: true,
		$onmozfullscreenerror: true,
		$outerHeight: true,
		$outerWidth: true,
		$pageXOffset: true,
		$pageYOffset: true,
		$parent: true,
		$scrollLeft: true,
		$scrollTop: true,
		$scrollX: true,
		$scrollY: true,
		$self: true,
		$webkitIndexedDB: true,
		$webkitStorageInfo: true,
		$window: true
	};
	var hasAutomationEqualityBug = (function () {
		/* global window */
		if (typeof window === 'undefined') { return false; }
		for (var k in window) {
			try {
				if (!excludedKeys['$' + k] && has.call(window, k) && window[k] !== null && typeof window[k] === 'object') {
					try {
						equalsConstructorPrototype(window[k]);
					} catch (e) {
						return true;
					}
				}
			} catch (e) {
				return true;
			}
		}
		return false;
	}());
	var equalsConstructorPrototypeIfNotBuggy = function (o) {
		/* global window */
		if (typeof window === 'undefined' || !hasAutomationEqualityBug) {
			return equalsConstructorPrototype(o);
		}
		try {
			return equalsConstructorPrototype(o);
		} catch (e) {
			return false;
		}
	};

	keysShim = function keys(object) {
		var isObject = object !== null && typeof object === 'object';
		var isFunction = toStr.call(object) === '[object Function]';
		var isArguments = isArgs(object);
		var isString = isObject && toStr.call(object) === '[object String]';
		var theKeys = [];

		if (!isObject && !isFunction && !isArguments) {
			throw new TypeError('Object.keys called on a non-object');
		}

		var skipProto = hasProtoEnumBug && isFunction;
		if (isString && object.length > 0 && !has.call(object, 0)) {
			for (var i = 0; i < object.length; ++i) {
				theKeys.push(String(i));
			}
		}

		if (isArguments && object.length > 0) {
			for (var j = 0; j < object.length; ++j) {
				theKeys.push(String(j));
			}
		} else {
			for (var name in object) {
				if (!(skipProto && name === 'prototype') && has.call(object, name)) {
					theKeys.push(String(name));
				}
			}
		}

		if (hasDontEnumBug) {
			var skipConstructor = equalsConstructorPrototypeIfNotBuggy(object);

			for (var k = 0; k < dontEnums.length; ++k) {
				if (!(skipConstructor && dontEnums[k] === 'constructor') && has.call(object, dontEnums[k])) {
					theKeys.push(dontEnums[k]);
				}
			}
		}
		return theKeys;
	};
}
module.exports = keysShim;


/***/ }),
/* 472 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var hasToStringTag = typeof Symbol === 'function' && typeof Symbol.toStringTag === 'symbol';
var toStr = Object.prototype.toString;

var isStandardArguments = function isArguments(value) {
	if (hasToStringTag && value && typeof value === 'object' && Symbol.toStringTag in value) {
		return false;
	}
	return toStr.call(value) === '[object Arguments]';
};

var isLegacyArguments = function isArguments(value) {
	if (isStandardArguments(value)) {
		return true;
	}
	return value !== null &&
		typeof value === 'object' &&
		typeof value.length === 'number' &&
		value.length >= 0 &&
		toStr.call(value) !== '[object Array]' &&
		toStr.call(value.callee) === '[object Function]';
};

var supportsStandardArguments = (function () {
	return isStandardArguments(arguments);
}());

isStandardArguments.isLegacyArguments = isLegacyArguments; // for tests

module.exports = supportsStandardArguments ? isStandardArguments : isLegacyArguments;


/***/ }),
/* 473 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var define = __webpack_require__(108);
var callBind = __webpack_require__(274);

var implementation = __webpack_require__(277);
var getPolyfill = __webpack_require__(278);
var shim = __webpack_require__(477);

var polyfill = callBind(getPolyfill(), Object);

define(polyfill, {
	getPolyfill: getPolyfill,
	implementation: implementation,
	shim: shim
});

module.exports = polyfill;


/***/ }),
/* 474 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


/* eslint no-invalid-this: 1 */

var ERROR_MESSAGE = 'Function.prototype.bind called on incompatible ';
var slice = Array.prototype.slice;
var toStr = Object.prototype.toString;
var funcType = '[object Function]';

module.exports = function bind(that) {
    var target = this;
    if (typeof target !== 'function' || toStr.call(target) !== funcType) {
        throw new TypeError(ERROR_MESSAGE + target);
    }
    var args = slice.call(arguments, 1);

    var bound;
    var binder = function () {
        if (this instanceof bound) {
            var result = target.apply(
                this,
                args.concat(slice.call(arguments))
            );
            if (Object(result) === result) {
                return result;
            }
            return this;
        } else {
            return target.apply(
                that,
                args.concat(slice.call(arguments))
            );
        }
    };

    var boundLength = Math.max(0, target.length - args.length);
    var boundArgs = [];
    for (var i = 0; i < boundLength; i++) {
        boundArgs.push('$' + i);
    }

    bound = Function('binder', 'return function (' + boundArgs.join(',') + '){ return binder.apply(this,arguments); }')(binder);

    if (target.prototype) {
        var Empty = function Empty() {};
        Empty.prototype = target.prototype;
        bound.prototype = new Empty();
        Empty.prototype = null;
    }

    return bound;
};


/***/ }),
/* 475 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


/* globals
	Atomics,
	SharedArrayBuffer,
*/

var undefined;

var $TypeError = TypeError;

var $gOPD = Object.getOwnPropertyDescriptor;
if ($gOPD) {
	try {
		$gOPD({}, '');
	} catch (e) {
		$gOPD = null; // this is IE 8, which has a broken gOPD
	}
}

var throwTypeError = function () { throw new $TypeError(); };
var ThrowTypeError = $gOPD
	? (function () {
		try {
			// eslint-disable-next-line no-unused-expressions, no-caller, no-restricted-properties
			arguments.callee; // IE 8 does not throw here
			return throwTypeError;
		} catch (calleeThrows) {
			try {
				// IE 8 throws on Object.getOwnPropertyDescriptor(arguments, '')
				return $gOPD(arguments, 'callee').get;
			} catch (gOPDthrows) {
				return throwTypeError;
			}
		}
	}())
	: throwTypeError;

var hasSymbols = __webpack_require__(276)();

var getProto = Object.getPrototypeOf || function (x) { return x.__proto__; }; // eslint-disable-line no-proto

var generator; // = function * () {};
var generatorFunction = generator ? getProto(generator) : undefined;
var asyncFn; // async function() {};
var asyncFunction = asyncFn ? asyncFn.constructor : undefined;
var asyncGen; // async function * () {};
var asyncGenFunction = asyncGen ? getProto(asyncGen) : undefined;
var asyncGenIterator = asyncGen ? asyncGen() : undefined;

var TypedArray = typeof Uint8Array === 'undefined' ? undefined : getProto(Uint8Array);

var INTRINSICS = {
	'%Array%': Array,
	'%ArrayBuffer%': typeof ArrayBuffer === 'undefined' ? undefined : ArrayBuffer,
	'%ArrayBufferPrototype%': typeof ArrayBuffer === 'undefined' ? undefined : ArrayBuffer.prototype,
	'%ArrayIteratorPrototype%': hasSymbols ? getProto([][Symbol.iterator]()) : undefined,
	'%ArrayPrototype%': Array.prototype,
	'%ArrayProto_entries%': Array.prototype.entries,
	'%ArrayProto_forEach%': Array.prototype.forEach,
	'%ArrayProto_keys%': Array.prototype.keys,
	'%ArrayProto_values%': Array.prototype.values,
	'%AsyncFromSyncIteratorPrototype%': undefined,
	'%AsyncFunction%': asyncFunction,
	'%AsyncFunctionPrototype%': asyncFunction ? asyncFunction.prototype : undefined,
	'%AsyncGenerator%': asyncGen ? getProto(asyncGenIterator) : undefined,
	'%AsyncGeneratorFunction%': asyncGenFunction,
	'%AsyncGeneratorPrototype%': asyncGenFunction ? asyncGenFunction.prototype : undefined,
	'%AsyncIteratorPrototype%': asyncGenIterator && hasSymbols && Symbol.asyncIterator ? asyncGenIterator[Symbol.asyncIterator]() : undefined,
	'%Atomics%': typeof Atomics === 'undefined' ? undefined : Atomics,
	'%Boolean%': Boolean,
	'%BooleanPrototype%': Boolean.prototype,
	'%DataView%': typeof DataView === 'undefined' ? undefined : DataView,
	'%DataViewPrototype%': typeof DataView === 'undefined' ? undefined : DataView.prototype,
	'%Date%': Date,
	'%DatePrototype%': Date.prototype,
	'%decodeURI%': decodeURI,
	'%decodeURIComponent%': decodeURIComponent,
	'%encodeURI%': encodeURI,
	'%encodeURIComponent%': encodeURIComponent,
	'%Error%': Error,
	'%ErrorPrototype%': Error.prototype,
	'%eval%': eval, // eslint-disable-line no-eval
	'%EvalError%': EvalError,
	'%EvalErrorPrototype%': EvalError.prototype,
	'%Float32Array%': typeof Float32Array === 'undefined' ? undefined : Float32Array,
	'%Float32ArrayPrototype%': typeof Float32Array === 'undefined' ? undefined : Float32Array.prototype,
	'%Float64Array%': typeof Float64Array === 'undefined' ? undefined : Float64Array,
	'%Float64ArrayPrototype%': typeof Float64Array === 'undefined' ? undefined : Float64Array.prototype,
	'%Function%': Function,
	'%FunctionPrototype%': Function.prototype,
	'%Generator%': generator ? getProto(generator()) : undefined,
	'%GeneratorFunction%': generatorFunction,
	'%GeneratorPrototype%': generatorFunction ? generatorFunction.prototype : undefined,
	'%Int8Array%': typeof Int8Array === 'undefined' ? undefined : Int8Array,
	'%Int8ArrayPrototype%': typeof Int8Array === 'undefined' ? undefined : Int8Array.prototype,
	'%Int16Array%': typeof Int16Array === 'undefined' ? undefined : Int16Array,
	'%Int16ArrayPrototype%': typeof Int16Array === 'undefined' ? undefined : Int8Array.prototype,
	'%Int32Array%': typeof Int32Array === 'undefined' ? undefined : Int32Array,
	'%Int32ArrayPrototype%': typeof Int32Array === 'undefined' ? undefined : Int32Array.prototype,
	'%isFinite%': isFinite,
	'%isNaN%': isNaN,
	'%IteratorPrototype%': hasSymbols ? getProto(getProto([][Symbol.iterator]())) : undefined,
	'%JSON%': typeof JSON === 'object' ? JSON : undefined,
	'%JSONParse%': typeof JSON === 'object' ? JSON.parse : undefined,
	'%Map%': typeof Map === 'undefined' ? undefined : Map,
	'%MapIteratorPrototype%': typeof Map === 'undefined' || !hasSymbols ? undefined : getProto(new Map()[Symbol.iterator]()),
	'%MapPrototype%': typeof Map === 'undefined' ? undefined : Map.prototype,
	'%Math%': Math,
	'%Number%': Number,
	'%NumberPrototype%': Number.prototype,
	'%Object%': Object,
	'%ObjectPrototype%': Object.prototype,
	'%ObjProto_toString%': Object.prototype.toString,
	'%ObjProto_valueOf%': Object.prototype.valueOf,
	'%parseFloat%': parseFloat,
	'%parseInt%': parseInt,
	'%Promise%': typeof Promise === 'undefined' ? undefined : Promise,
	'%PromisePrototype%': typeof Promise === 'undefined' ? undefined : Promise.prototype,
	'%PromiseProto_then%': typeof Promise === 'undefined' ? undefined : Promise.prototype.then,
	'%Promise_all%': typeof Promise === 'undefined' ? undefined : Promise.all,
	'%Promise_reject%': typeof Promise === 'undefined' ? undefined : Promise.reject,
	'%Promise_resolve%': typeof Promise === 'undefined' ? undefined : Promise.resolve,
	'%Proxy%': typeof Proxy === 'undefined' ? undefined : Proxy,
	'%RangeError%': RangeError,
	'%RangeErrorPrototype%': RangeError.prototype,
	'%ReferenceError%': ReferenceError,
	'%ReferenceErrorPrototype%': ReferenceError.prototype,
	'%Reflect%': typeof Reflect === 'undefined' ? undefined : Reflect,
	'%RegExp%': RegExp,
	'%RegExpPrototype%': RegExp.prototype,
	'%Set%': typeof Set === 'undefined' ? undefined : Set,
	'%SetIteratorPrototype%': typeof Set === 'undefined' || !hasSymbols ? undefined : getProto(new Set()[Symbol.iterator]()),
	'%SetPrototype%': typeof Set === 'undefined' ? undefined : Set.prototype,
	'%SharedArrayBuffer%': typeof SharedArrayBuffer === 'undefined' ? undefined : SharedArrayBuffer,
	'%SharedArrayBufferPrototype%': typeof SharedArrayBuffer === 'undefined' ? undefined : SharedArrayBuffer.prototype,
	'%String%': String,
	'%StringIteratorPrototype%': hasSymbols ? getProto(''[Symbol.iterator]()) : undefined,
	'%StringPrototype%': String.prototype,
	'%Symbol%': hasSymbols ? Symbol : undefined,
	'%SymbolPrototype%': hasSymbols ? Symbol.prototype : undefined,
	'%SyntaxError%': SyntaxError,
	'%SyntaxErrorPrototype%': SyntaxError.prototype,
	'%ThrowTypeError%': ThrowTypeError,
	'%TypedArray%': TypedArray,
	'%TypedArrayPrototype%': TypedArray ? TypedArray.prototype : undefined,
	'%TypeError%': $TypeError,
	'%TypeErrorPrototype%': $TypeError.prototype,
	'%Uint8Array%': typeof Uint8Array === 'undefined' ? undefined : Uint8Array,
	'%Uint8ArrayPrototype%': typeof Uint8Array === 'undefined' ? undefined : Uint8Array.prototype,
	'%Uint8ClampedArray%': typeof Uint8ClampedArray === 'undefined' ? undefined : Uint8ClampedArray,
	'%Uint8ClampedArrayPrototype%': typeof Uint8ClampedArray === 'undefined' ? undefined : Uint8ClampedArray.prototype,
	'%Uint16Array%': typeof Uint16Array === 'undefined' ? undefined : Uint16Array,
	'%Uint16ArrayPrototype%': typeof Uint16Array === 'undefined' ? undefined : Uint16Array.prototype,
	'%Uint32Array%': typeof Uint32Array === 'undefined' ? undefined : Uint32Array,
	'%Uint32ArrayPrototype%': typeof Uint32Array === 'undefined' ? undefined : Uint32Array.prototype,
	'%URIError%': URIError,
	'%URIErrorPrototype%': URIError.prototype,
	'%WeakMap%': typeof WeakMap === 'undefined' ? undefined : WeakMap,
	'%WeakMapPrototype%': typeof WeakMap === 'undefined' ? undefined : WeakMap.prototype,
	'%WeakSet%': typeof WeakSet === 'undefined' ? undefined : WeakSet,
	'%WeakSetPrototype%': typeof WeakSet === 'undefined' ? undefined : WeakSet.prototype
};

var bind = __webpack_require__(275);
var $replace = bind.call(Function.call, String.prototype.replace);

/* adapted from https://github.com/lodash/lodash/blob/4.17.15/dist/lodash.js#L6735-L6744 */
var rePropName = /[^%.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|%$))/g;
var reEscapeChar = /\\(\\)?/g; /** Used to match backslashes in property paths. */
var stringToPath = function stringToPath(string) {
	var result = [];
	$replace(string, rePropName, function (match, number, quote, subString) {
		result[result.length] = quote ? $replace(subString, reEscapeChar, '$1') : (number || match);
	});
	return result;
};
/* end adaptation */

var getBaseIntrinsic = function getBaseIntrinsic(name, allowMissing) {
	if (!(name in INTRINSICS)) {
		throw new SyntaxError('intrinsic ' + name + ' does not exist!');
	}

	// istanbul ignore if // hopefully this is impossible to test :-)
	if (typeof INTRINSICS[name] === 'undefined' && !allowMissing) {
		throw new $TypeError('intrinsic ' + name + ' exists, but is not available. Please file an issue!');
	}

	return INTRINSICS[name];
};

module.exports = function GetIntrinsic(name, allowMissing) {
	if (typeof name !== 'string' || name.length === 0) {
		throw new TypeError('intrinsic name must be a non-empty string');
	}
	if (arguments.length > 1 && typeof allowMissing !== 'boolean') {
		throw new TypeError('"allowMissing" argument must be a boolean');
	}

	var parts = stringToPath(name);

	var value = getBaseIntrinsic('%' + (parts.length > 0 ? parts[0] : '') + '%', allowMissing);
	for (var i = 1; i < parts.length; i += 1) {
		if (value != null) {
			if ($gOPD && (i + 1) >= parts.length) {
				var desc = $gOPD(value, parts[i]);
				if (!allowMissing && !(parts[i] in value)) {
					throw new $TypeError('base intrinsic for ' + name + ' exists, but the property is not available.');
				}
				value = desc ? (desc.get || desc.value) : value[parts[i]];
			} else {
				value = value[parts[i]];
			}
		}
	}
	return value;
};


/***/ }),
/* 476 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


/* eslint complexity: [2, 18], max-statements: [2, 33] */
module.exports = function hasSymbols() {
	if (typeof Symbol !== 'function' || typeof Object.getOwnPropertySymbols !== 'function') { return false; }
	if (typeof Symbol.iterator === 'symbol') { return true; }

	var obj = {};
	var sym = Symbol('test');
	var symObj = Object(sym);
	if (typeof sym === 'string') { return false; }

	if (Object.prototype.toString.call(sym) !== '[object Symbol]') { return false; }
	if (Object.prototype.toString.call(symObj) !== '[object Symbol]') { return false; }

	// temp disabled per https://github.com/ljharb/object.assign/issues/17
	// if (sym instanceof Symbol) { return false; }
	// temp disabled per https://github.com/WebReflection/get-own-property-symbols/issues/4
	// if (!(symObj instanceof Symbol)) { return false; }

	// if (typeof Symbol.prototype.toString !== 'function') { return false; }
	// if (String(sym) !== Symbol.prototype.toString.call(sym)) { return false; }

	var symVal = 42;
	obj[sym] = symVal;
	for (sym in obj) { return false; } // eslint-disable-line no-restricted-syntax
	if (typeof Object.keys === 'function' && Object.keys(obj).length !== 0) { return false; }

	if (typeof Object.getOwnPropertyNames === 'function' && Object.getOwnPropertyNames(obj).length !== 0) { return false; }

	var syms = Object.getOwnPropertySymbols(obj);
	if (syms.length !== 1 || syms[0] !== sym) { return false; }

	if (!Object.prototype.propertyIsEnumerable.call(obj, sym)) { return false; }

	if (typeof Object.getOwnPropertyDescriptor === 'function') {
		var descriptor = Object.getOwnPropertyDescriptor(obj, sym);
		if (descriptor.value !== symVal || descriptor.enumerable !== true) { return false; }
	}

	return true;
};


/***/ }),
/* 477 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var getPolyfill = __webpack_require__(278);
var define = __webpack_require__(108);

module.exports = function shimObjectIs() {
	var polyfill = getPolyfill();
	define(Object, { is: polyfill }, {
		is: function testObjectIs() {
			return Object.is !== polyfill;
		}
	});
	return polyfill;
};


/***/ }),
/* 478 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var hasSymbols = __webpack_require__(276)();
var hasToStringTag = hasSymbols && typeof Symbol.toStringTag === 'symbol';
var regexExec;
var isRegexMarker;
var badStringifier;

if (hasToStringTag) {
	regexExec = Function.call.bind(RegExp.prototype.exec);
	isRegexMarker = {};

	var throwRegexMarker = function () {
		throw isRegexMarker;
	};
	badStringifier = {
		toString: throwRegexMarker,
		valueOf: throwRegexMarker
	};

	if (typeof Symbol.toPrimitive === 'symbol') {
		badStringifier[Symbol.toPrimitive] = throwRegexMarker;
	}
}

var toStr = Object.prototype.toString;
var regexClass = '[object RegExp]';

module.exports = hasToStringTag
	// eslint-disable-next-line consistent-return
	? function isRegex(value) {
		if (!value || typeof value !== 'object') {
			return false;
		}

		try {
			regexExec(value, badStringifier);
		} catch (e) {
			return e === isRegexMarker;
		}
	}
	: function isRegex(value) {
		// In older browsers, typeof regex incorrectly returns 'function'
		if (!value || (typeof value !== 'object' && typeof value !== 'function')) {
			return false;
		}

		return toStr.call(value) === regexClass;
	};


/***/ }),
/* 479 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var define = __webpack_require__(108);
var callBind = __webpack_require__(274);

var implementation = __webpack_require__(279);
var getPolyfill = __webpack_require__(280);
var shim = __webpack_require__(480);

var flagsBound = callBind(implementation);

define(flagsBound, {
	getPolyfill: getPolyfill,
	implementation: implementation,
	shim: shim
});

module.exports = flagsBound;


/***/ }),
/* 480 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var supportsDescriptors = __webpack_require__(108).supportsDescriptors;
var getPolyfill = __webpack_require__(280);
var gOPD = Object.getOwnPropertyDescriptor;
var defineProperty = Object.defineProperty;
var TypeErr = TypeError;
var getProto = Object.getPrototypeOf;
var regex = /a/;

module.exports = function shimFlags() {
	if (!supportsDescriptors || !getProto) {
		throw new TypeErr('RegExp.prototype.flags requires a true ES5 environment that supports property descriptors');
	}
	var polyfill = getPolyfill();
	var proto = getProto(regex);
	var descriptor = gOPD(proto, 'flags');
	if (!descriptor || descriptor.get !== polyfill) {
		defineProperty(proto, 'flags', {
			configurable: true,
			enumerable: false,
			get: polyfill
		});
	}
	return polyfill;
};


/***/ }),
/* 481 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var getDay = Date.prototype.getDay;
var tryDateObject = function tryDateGetDayCall(value) {
	try {
		getDay.call(value);
		return true;
	} catch (e) {
		return false;
	}
};

var toStr = Object.prototype.toString;
var dateClass = '[object Date]';
var hasToStringTag = typeof Symbol === 'function' && typeof Symbol.toStringTag === 'symbol';

module.exports = function isDateObject(value) {
	if (typeof value !== 'object' || value === null) {
		return false;
	}
	return hasToStringTag ? tryDateObject(value) : toStr.call(value) === dateClass;
};


/***/ }),
/* 482 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(483), __esModule: true };

/***/ }),
/* 483 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(484);
module.exports = __webpack_require__(7).Object.assign;


/***/ }),
/* 484 */
/***/ (function(module, exports, __webpack_require__) {

// 19.1.3.1 Object.assign(target, source)
var $export = __webpack_require__(9);

$export($export.S + $export.F, 'Object', { assign: __webpack_require__(485) });


/***/ }),
/* 485 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

// 19.1.2.1 Object.assign(target, source, ...)
var DESCRIPTORS = __webpack_require__(23);
var getKeys = __webpack_require__(105);
var gOPS = __webpack_require__(188);
var pIE = __webpack_require__(128);
var toObject = __webpack_require__(90);
var IObject = __webpack_require__(172);
var $assign = Object.assign;

// should work with symbols and should have deterministic property order (V8 bug)
module.exports = !$assign || __webpack_require__(52)(function () {
  var A = {};
  var B = {};
  // eslint-disable-next-line no-undef
  var S = Symbol();
  var K = 'abcdefghijklmnopqrst';
  A[S] = 7;
  K.split('').forEach(function (k) { B[k] = k; });
  return $assign({}, A)[S] != 7 || Object.keys($assign({}, B)).join('') != K;
}) ? function assign(target, source) { // eslint-disable-line no-unused-vars
  var T = toObject(target);
  var aLen = arguments.length;
  var index = 1;
  var getSymbols = gOPS.f;
  var isEnum = pIE.f;
  while (aLen > index) {
    var S = IObject(arguments[index++]);
    var keys = getSymbols ? getKeys(S).concat(getSymbols(S)) : getKeys(S);
    var length = keys.length;
    var j = 0;
    var key;
    while (length > j) {
      key = keys[j++];
      if (!DESCRIPTORS || isEnum.call(S, key)) T[key] = S[key];
    }
  } return T;
} : $assign;


/***/ }),
/* 486 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(487), __esModule: true };

/***/ }),
/* 487 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(488);
module.exports = __webpack_require__(7).String.fromCodePoint;


/***/ }),
/* 488 */
/***/ (function(module, exports, __webpack_require__) {

var $export = __webpack_require__(9);
var toAbsoluteIndex = __webpack_require__(259);
var fromCharCode = String.fromCharCode;
var $fromCodePoint = String.fromCodePoint;

// length should be 1, old FF problem
$export($export.S + $export.F * (!!$fromCodePoint && $fromCodePoint.length != 1), 'String', {
  // 21.1.2.2 String.fromCodePoint(...codePoints)
  fromCodePoint: function fromCodePoint(x) { // eslint-disable-line no-unused-vars
    var res = [];
    var aLen = arguments.length;
    var i = 0;
    var code;
    while (aLen > i) {
      code = +arguments[i++];
      if (toAbsoluteIndex(code, 0x10ffff) !== code) throw RangeError(code + ' is not a valid code point');
      res.push(code < 0x10000
        ? fromCharCode(code)
        : fromCharCode(((code -= 0x10000) >> 10) + 0xd800, code % 0x400 + 0xdc00)
      );
    } return res.join('');
  }
});


/***/ }),
/* 489 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(490), __esModule: true };

/***/ }),
/* 490 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(91);
__webpack_require__(491);
module.exports = __webpack_require__(7).Array.from;


/***/ }),
/* 491 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var ctx = __webpack_require__(33);
var $export = __webpack_require__(9);
var toObject = __webpack_require__(90);
var call = __webpack_require__(265);
var isArrayIter = __webpack_require__(266);
var toLength = __webpack_require__(130);
var createProperty = __webpack_require__(492);
var getIterFn = __webpack_require__(183);

$export($export.S + $export.F * !__webpack_require__(281)(function (iter) { Array.from(iter); }), 'Array', {
  // 22.1.2.1 Array.from(arrayLike, mapfn = undefined, thisArg = undefined)
  from: function from(arrayLike /* , mapfn = undefined, thisArg = undefined */) {
    var O = toObject(arrayLike);
    var C = typeof this == 'function' ? this : Array;
    var aLen = arguments.length;
    var mapfn = aLen > 1 ? arguments[1] : undefined;
    var mapping = mapfn !== undefined;
    var index = 0;
    var iterFn = getIterFn(O);
    var length, result, step, iterator;
    if (mapping) mapfn = ctx(mapfn, aLen > 2 ? arguments[2] : undefined, 2);
    // if object isn't iterable or it's array with default iterator - use simple case
    if (iterFn != undefined && !(C == Array && isArrayIter(iterFn))) {
      for (iterator = iterFn.call(O), result = new C(); !(step = iterator.next()).done; index++) {
        createProperty(result, index, mapping ? call(iterator, mapfn, [step.value, index], true) : step.value);
      }
    } else {
      length = toLength(O.length);
      for (result = new C(length); length > index; index++) {
        createProperty(result, index, mapping ? mapfn(O[index], index) : O[index]);
      }
    }
    result.length = index;
    return result;
  }
});


/***/ }),
/* 492 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $defineProperty = __webpack_require__(26);
var createDesc = __webpack_require__(100);

module.exports = function (object, index, value) {
  if (index in object) $defineProperty.f(object, index, createDesc(0, value));
  else object[index] = value;
};


/***/ }),
/* 493 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(494), __esModule: true };

/***/ }),
/* 494 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(133);
__webpack_require__(91);
__webpack_require__(102);
__webpack_require__(495);
__webpack_require__(496);
__webpack_require__(497);
__webpack_require__(498);
module.exports = __webpack_require__(7).Set;


/***/ }),
/* 495 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var strong = __webpack_require__(264);
var validate = __webpack_require__(191);
var SET = 'Set';

// 23.2 Set Objects
module.exports = __webpack_require__(268)(SET, function (get) {
  return function Set() { return get(this, arguments.length > 0 ? arguments[0] : undefined); };
}, {
  // 23.2.3.1 Set.prototype.add(value)
  add: function add(value) {
    return strong.def(validate(this, SET), value = value === 0 ? 0 : value, value);
  }
}, strong);


/***/ }),
/* 496 */
/***/ (function(module, exports, __webpack_require__) {

// https://github.com/DavidBruant/Map-Set.prototype.toJSON
var $export = __webpack_require__(9);

$export($export.P + $export.R, 'Set', { toJSON: __webpack_require__(269)('Set') });


/***/ }),
/* 497 */
/***/ (function(module, exports, __webpack_require__) {

// https://tc39.github.io/proposal-setmap-offrom/#sec-set.of
__webpack_require__(270)('Set');


/***/ }),
/* 498 */
/***/ (function(module, exports, __webpack_require__) {

// https://tc39.github.io/proposal-setmap-offrom/#sec-set.from
__webpack_require__(271)('Set');


/***/ }),
/* 499 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(500);

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _unicodeTrie = _interopRequireDefault(__webpack_require__(501));

var _base64Js = _interopRequireDefault(__webpack_require__(503));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var categories = ["Cc", "Zs", "Po", "Sc", "Ps", "Pe", "Sm", "Pd", "Nd", "Lu", "Sk", "Pc", "Ll", "So", "Lo", "Pi", "Cf", "No", "Pf", "Lt", "Lm", "Mn", "Me", "Mc", "Nl", "Zl", "Zp", "Cs", "Co"];
var combiningClasses = ["Not_Reordered", "Above", "Above_Right", "Below", "Attached_Above_Right", "Attached_Below", "Overlay", "Iota_Subscript", "Double_Below", "Double_Above", "Below_Right", "Above_Left", "CCC10", "CCC11", "CCC12", "CCC13", "CCC14", "CCC15", "CCC16", "CCC17", "CCC18", "CCC19", "CCC20", "CCC21", "CCC22", "CCC23", "CCC24", "CCC25", "CCC30", "CCC31", "CCC32", "CCC27", "CCC28", "CCC29", "CCC33", "CCC34", "CCC35", "CCC36", "Nukta", "Virama", "CCC84", "CCC91", "CCC103", "CCC107", "CCC118", "CCC122", "CCC129", "CCC130", "CCC132", "Attached_Above", "Below_Left", "Left", "Kana_Voicing", "CCC26", "Right"];
var scripts = ["Common", "Latin", "Bopomofo", "Inherited", "Greek", "Coptic", "Cyrillic", "Armenian", "Hebrew", "Arabic", "Syriac", "Thaana", "Nko", "Samaritan", "Mandaic", "Devanagari", "Bengali", "Gurmukhi", "Gujarati", "Oriya", "Tamil", "Telugu", "Kannada", "Malayalam", "Sinhala", "Thai", "Lao", "Tibetan", "Myanmar", "Georgian", "Hangul", "Ethiopic", "Cherokee", "Canadian_Aboriginal", "Ogham", "Runic", "Tagalog", "Hanunoo", "Buhid", "Tagbanwa", "Khmer", "Mongolian", "Limbu", "Tai_Le", "New_Tai_Lue", "Buginese", "Tai_Tham", "Balinese", "Sundanese", "Batak", "Lepcha", "Ol_Chiki", "Braille", "Glagolitic", "Tifinagh", "Han", "Hiragana", "Katakana", "Yi", "Lisu", "Vai", "Bamum", "Syloti_Nagri", "Phags_Pa", "Saurashtra", "Kayah_Li", "Rejang", "Javanese", "Cham", "Tai_Viet", "Meetei_Mayek", "null", "Linear_B", "Lycian", "Carian", "Old_Italic", "Gothic", "Old_Permic", "Ugaritic", "Old_Persian", "Deseret", "Shavian", "Osmanya", "Osage", "Elbasan", "Caucasian_Albanian", "Linear_A", "Cypriot", "Imperial_Aramaic", "Palmyrene", "Nabataean", "Hatran", "Phoenician", "Lydian", "Meroitic_Hieroglyphs", "Meroitic_Cursive", "Kharoshthi", "Old_South_Arabian", "Old_North_Arabian", "Manichaean", "Avestan", "Inscriptional_Parthian", "Inscriptional_Pahlavi", "Psalter_Pahlavi", "Old_Turkic", "Old_Hungarian", "Hanifi_Rohingya", "Old_Sogdian", "Sogdian", "Elymaic", "Brahmi", "Kaithi", "Sora_Sompeng", "Chakma", "Mahajani", "Sharada", "Khojki", "Multani", "Khudawadi", "Grantha", "Newa", "Tirhuta", "Siddham", "Modi", "Takri", "Ahom", "Dogra", "Warang_Citi", "Nandinagari", "Zanabazar_Square", "Soyombo", "Pau_Cin_Hau", "Bhaiksuki", "Marchen", "Masaram_Gondi", "Gunjala_Gondi", "Makasar", "Cuneiform", "Egyptian_Hieroglyphs", "Anatolian_Hieroglyphs", "Mro", "Bassa_Vah", "Pahawh_Hmong", "Medefaidrin", "Miao", "Tangut", "Nushu", "Duployan", "SignWriting", "Nyiakeng_Puachue_Hmong", "Wancho", "Mende_Kikakui", "Adlam"];
var eaw = ["N", "Na", "A", "W", "H", "F"];
var data = {
  categories: categories,
  combiningClasses: combiningClasses,
  scripts: scripts,
  eaw: eaw
};
var data$1 = "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";
var trieData = {
  data: data$1
};

var log2 = Math.log2 || function (n) {
  return Math.log(n) / Math.LN2;
};

var bits = function bits(n) {
  return log2(n) + 1 | 0;
};

var buildUnicodeProperties = function buildUnicodeProperties(data, trie) {
  // compute the number of bits stored for each field
  var CATEGORY_BITS = bits(data.categories.length - 1);
  var COMBINING_BITS = bits(data.combiningClasses.length - 1);
  var SCRIPT_BITS = bits(data.scripts.length - 1);
  var EAW_BITS = bits(data.eaw.length - 1);
  var NUMBER_BITS = 10; // compute shift and mask values for each field

  var CATEGORY_SHIFT = COMBINING_BITS + SCRIPT_BITS + EAW_BITS + NUMBER_BITS;
  var COMBINING_SHIFT = SCRIPT_BITS + EAW_BITS + NUMBER_BITS;
  var SCRIPT_SHIFT = EAW_BITS + NUMBER_BITS;
  var EAW_SHIFT = NUMBER_BITS;
  var CATEGORY_MASK = (1 << CATEGORY_BITS) - 1;
  var COMBINING_MASK = (1 << COMBINING_BITS) - 1;
  var SCRIPT_MASK = (1 << SCRIPT_BITS) - 1;
  var EAW_MASK = (1 << EAW_BITS) - 1;
  var NUMBER_MASK = (1 << NUMBER_BITS) - 1;

  var getCategory = function getCategory(codePoint) {
    var val = trie.get(codePoint);
    return data.categories[val >> CATEGORY_SHIFT & CATEGORY_MASK];
  };

  var getCombiningClass = function getCombiningClass(codePoint) {
    var val = trie.get(codePoint);
    return data.combiningClasses[val >> COMBINING_SHIFT & COMBINING_MASK];
  };

  var getScript = function getScript(codePoint) {
    var val = trie.get(codePoint);
    return data.scripts[val >> SCRIPT_SHIFT & SCRIPT_MASK];
  };

  var getEastAsianWidth = function getEastAsianWidth(codePoint) {
    var val = trie.get(codePoint);
    return data.eaw[val >> EAW_SHIFT & EAW_MASK];
  };

  var getNumericValue = function getNumericValue(codePoint) {
    var val = trie.get(codePoint);
    var num = val & NUMBER_MASK;

    if (num === 0) {
      return null;
    } else if (num <= 50) {
      return num - 1;
    } else if (num < 0x1e0) {
      var numerator = (num >> 4) - 12;
      var denominator = (num & 0xf) + 1;
      return numerator / denominator;
    } else if (num < 0x300) {
      val = (num >> 5) - 14;
      var exp = (num & 0x1f) + 2;

      while (exp > 0) {
        val *= 10;
        exp--;
      }

      return val;
    } else {
      val = (num >> 2) - 0xbf;

      var _exp = (num & 3) + 1;

      while (_exp > 0) {
        val *= 60;
        _exp--;
      }

      return val;
    }
  };

  var isAlphabetic = function isAlphabetic(codePoint) {
    var category = getCategory(codePoint);
    return category === 'Lu' || category === 'Ll' || category === 'Lt' || category === 'Lm' || category === 'Lo' || category === 'Nl';
  };

  var isDigit = function isDigit(codePoint) {
    return getCategory(codePoint) === 'Nd';
  };

  var isPunctuation = function isPunctuation(codePoint) {
    var category = getCategory(codePoint);
    return category === 'Pc' || category === 'Pd' || category === 'Pe' || category === 'Pf' || category === 'Pi' || category === 'Po' || category === 'Ps';
  };

  var isLowerCase = function isLowerCase(codePoint) {
    return getCategory(codePoint) === 'Ll';
  };

  var isUpperCase = function isUpperCase(codePoint) {
    return getCategory(codePoint) === 'Lu';
  };

  var isTitleCase = function isTitleCase(codePoint) {
    return getCategory(codePoint) === 'Lt';
  };

  var isWhiteSpace = function isWhiteSpace(codePoint) {
    var category = getCategory(codePoint);
    return category === 'Zs' || category === 'Zl' || category === 'Zp';
  };

  var isBaseForm = function isBaseForm(codePoint) {
    var category = getCategory(codePoint);
    return category === 'Nd' || category === 'No' || category === 'Nl' || category === 'Lu' || category === 'Ll' || category === 'Lt' || category === 'Lm' || category === 'Lo' || category === 'Me' || category === 'Mc';
  };

  var isMark = function isMark(codePoint) {
    var category = getCategory(codePoint);
    return category === 'Mn' || category === 'Me' || category === 'Mc';
  };

  return {
    getCategory: getCategory,
    getCombiningClass: getCombiningClass,
    getScript: getScript,
    getEastAsianWidth: getEastAsianWidth,
    getNumericValue: getNumericValue,
    isAlphabetic: isAlphabetic,
    isDigit: isDigit,
    isPunctuation: isPunctuation,
    isLowerCase: isLowerCase,
    isUpperCase: isUpperCase,
    isTitleCase: isTitleCase,
    isWhiteSpace: isWhiteSpace,
    isBaseForm: isBaseForm,
    isMark: isMark
  };
};

var trie = new _unicodeTrie.default(_base64Js.default.toByteArray(trieData.data));
var unicodeProperties = buildUnicodeProperties(data, trie);
var _default = unicodeProperties;
exports.default = _default;

/***/ }),
/* 500 */
/***/ (function(module, exports, __webpack_require__) {

var $ = __webpack_require__(1);

var log = Math.log;
var LN2 = Math.LN2;

// `Math.log2` method
// https://tc39.github.io/ecma262/#sec-math.log2
$({ target: 'Math', stat: true }, {
  log2: function log2(x) {
    return log(x) / LN2;
  }
});


/***/ }),
/* 501 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(28);

__webpack_require__(46);

__webpack_require__(61);

__webpack_require__(32);

__webpack_require__(62);

__webpack_require__(134);

__webpack_require__(63);

__webpack_require__(64);

__webpack_require__(65);

__webpack_require__(66);

__webpack_require__(67);

__webpack_require__(68);

__webpack_require__(69);

__webpack_require__(70);

__webpack_require__(71);

__webpack_require__(72);

__webpack_require__(73);

__webpack_require__(74);

__webpack_require__(75);

__webpack_require__(76);

__webpack_require__(77);

__webpack_require__(78);

__webpack_require__(79);

__webpack_require__(80);

__webpack_require__(81);

__webpack_require__(82);

__webpack_require__(83);

__webpack_require__(84);

__webpack_require__(85);

var inflate = __webpack_require__(135);

var _require = __webpack_require__(502),
    swap32LE = _require.swap32LE; // Shift size for getting the index-1 table offset.


var SHIFT_1 = 6 + 5; // Shift size for getting the index-2 table offset.

var SHIFT_2 = 5; // Difference between the two shift sizes,
// for getting an index-1 offset from an index-2 offset. 6=11-5

var SHIFT_1_2 = SHIFT_1 - SHIFT_2; // Number of index-1 entries for the BMP. 32=0x20
// This part of the index-1 table is omitted from the serialized form.

var OMITTED_BMP_INDEX_1_LENGTH = 0x10000 >> SHIFT_1; // Number of entries in an index-2 block. 64=0x40

var INDEX_2_BLOCK_LENGTH = 1 << SHIFT_1_2; // Mask for getting the lower bits for the in-index-2-block offset. */

var INDEX_2_MASK = INDEX_2_BLOCK_LENGTH - 1; // Shift size for shifting left the index array values.
// Increases possible data size with 16-bit index values at the cost
// of compactability.
// This requires data blocks to be aligned by DATA_GRANULARITY.

var INDEX_SHIFT = 2; // Number of entries in a data block. 32=0x20

var DATA_BLOCK_LENGTH = 1 << SHIFT_2; // Mask for getting the lower bits for the in-data-block offset.

var DATA_MASK = DATA_BLOCK_LENGTH - 1; // The part of the index-2 table for U+D800..U+DBFF stores values for
// lead surrogate code _units_ not code _points_.
// Values for lead surrogate code _points_ are indexed with this portion of the table.
// Length=32=0x20=0x400>>SHIFT_2. (There are 1024=0x400 lead surrogates.)

var LSCP_INDEX_2_OFFSET = 0x10000 >> SHIFT_2;
var LSCP_INDEX_2_LENGTH = 0x400 >> SHIFT_2; // Count the lengths of both BMP pieces. 2080=0x820

var INDEX_2_BMP_LENGTH = LSCP_INDEX_2_OFFSET + LSCP_INDEX_2_LENGTH; // The 2-byte UTF-8 version of the index-2 table follows at offset 2080=0x820.
// Length 32=0x20 for lead bytes C0..DF, regardless of SHIFT_2.

var UTF8_2B_INDEX_2_OFFSET = INDEX_2_BMP_LENGTH;
var UTF8_2B_INDEX_2_LENGTH = 0x800 >> 6; // U+0800 is the first code point after 2-byte UTF-8
// The index-1 table, only used for supplementary code points, at offset 2112=0x840.
// Variable length, for code points up to highStart, where the last single-value range starts.
// Maximum length 512=0x200=0x100000>>SHIFT_1.
// (For 0x100000 supplementary code points U+10000..U+10ffff.)
//
// The part of the index-2 table for supplementary code points starts
// after this index-1 table.
//
// Both the index-1 table and the following part of the index-2 table
// are omitted completely if there is only BMP data.

var INDEX_1_OFFSET = UTF8_2B_INDEX_2_OFFSET + UTF8_2B_INDEX_2_LENGTH; // The alignment size of a data block. Also the granularity for compaction.

var DATA_GRANULARITY = 1 << INDEX_SHIFT;

var UnicodeTrie = /*#__PURE__*/function () {
  function UnicodeTrie(data) {
    var isBuffer = typeof data.readUInt32BE === 'function' && typeof data.slice === 'function';

    if (isBuffer || data instanceof Uint8Array) {
      // read binary format
      var uncompressedLength;

      if (isBuffer) {
        this.highStart = data.readUInt32LE(0);
        this.errorValue = data.readUInt32LE(4);
        uncompressedLength = data.readUInt32LE(8);
        data = data.slice(12);
      } else {
        var view = new DataView(data.buffer);
        this.highStart = view.getUint32(0, true);
        this.errorValue = view.getUint32(4, true);
        uncompressedLength = view.getUint32(8, true);
        data = data.subarray(12);
      } // double inflate the actual trie data


      data = inflate(data, new Uint8Array(uncompressedLength));
      data = inflate(data, new Uint8Array(uncompressedLength)); // swap bytes from little-endian

      swap32LE(data);
      this.data = new Uint32Array(data.buffer);
    } else {
      // pre-parsed data
      var _data = data;
      this.data = _data.data;
      this.highStart = _data.highStart;
      this.errorValue = _data.errorValue;
    }
  }

  var _proto = UnicodeTrie.prototype;

  _proto.get = function get(codePoint) {
    var index;

    if (codePoint < 0 || codePoint > 0x10ffff) {
      return this.errorValue;
    }

    if (codePoint < 0xd800 || codePoint > 0xdbff && codePoint <= 0xffff) {
      // Ordinary BMP code point, excluding leading surrogates.
      // BMP uses a single level lookup.  BMP index starts at offset 0 in the index.
      // data is stored in the index array itself.
      index = (this.data[codePoint >> SHIFT_2] << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }

    if (codePoint <= 0xffff) {
      // Lead Surrogate Code Point.  A Separate index section is stored for
      // lead surrogate code units and code points.
      //   The main index has the code unit data.
      //   For this function, we need the code point data.
      index = (this.data[LSCP_INDEX_2_OFFSET + (codePoint - 0xd800 >> SHIFT_2)] << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }

    if (codePoint < this.highStart) {
      // Supplemental code point, use two-level lookup.
      index = this.data[INDEX_1_OFFSET - OMITTED_BMP_INDEX_1_LENGTH + (codePoint >> SHIFT_1)];
      index = this.data[index + (codePoint >> SHIFT_2 & INDEX_2_MASK)];
      index = (index << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }

    return this.data[this.data.length - DATA_GRANULARITY];
  };

  return UnicodeTrie;
}();

module.exports = UnicodeTrie;

/***/ }),
/* 502 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(28);

__webpack_require__(61);

__webpack_require__(32);

__webpack_require__(62);

__webpack_require__(134);

__webpack_require__(63);

__webpack_require__(64);

__webpack_require__(65);

__webpack_require__(66);

__webpack_require__(67);

__webpack_require__(68);

__webpack_require__(69);

__webpack_require__(70);

__webpack_require__(71);

__webpack_require__(72);

__webpack_require__(73);

__webpack_require__(74);

__webpack_require__(75);

__webpack_require__(76);

__webpack_require__(77);

__webpack_require__(78);

__webpack_require__(79);

__webpack_require__(80);

__webpack_require__(81);

__webpack_require__(82);

__webpack_require__(83);

__webpack_require__(84);

__webpack_require__(85);

var isBigEndian = new Uint8Array(new Uint32Array([0x12345678]).buffer)[0] === 0x12;

var swap = function swap(b, n, m) {
  var i = b[n];
  b[n] = b[m];
  b[m] = i;
};

var swap32 = function swap32(array) {
  var len = array.length;

  for (var i = 0; i < len; i += 4) {
    swap(array, i, i + 3);
    swap(array, i + 1, i + 2);
  }
};

var swap32LE = function swap32LE(array) {
  if (isBigEndian) {
    swap32(array);
  }
};

module.exports = {
  swap32LE: swap32LE
};

/***/ }),
/* 503 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(192);

__webpack_require__(28);

__webpack_require__(219);

__webpack_require__(61);

__webpack_require__(32);

__webpack_require__(62);

__webpack_require__(63);

__webpack_require__(64);

__webpack_require__(65);

__webpack_require__(66);

__webpack_require__(67);

__webpack_require__(68);

__webpack_require__(69);

__webpack_require__(70);

__webpack_require__(71);

__webpack_require__(72);

__webpack_require__(73);

__webpack_require__(74);

__webpack_require__(75);

__webpack_require__(76);

__webpack_require__(77);

__webpack_require__(78);

__webpack_require__(79);

__webpack_require__(80);

__webpack_require__(81);

__webpack_require__(82);

__webpack_require__(83);

__webpack_require__(84);

__webpack_require__(85);

exports.byteLength = byteLength;
exports.toByteArray = toByteArray;
exports.fromByteArray = fromByteArray;
var lookup = [];
var revLookup = [];
var Arr = typeof Uint8Array !== 'undefined' ? Uint8Array : Array;
var code = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

for (var i = 0, len = code.length; i < len; ++i) {
  lookup[i] = code[i];
  revLookup[code.charCodeAt(i)] = i;
} // Support decoding URL-safe base64 strings, as Node.js does.
// See: https://en.wikipedia.org/wiki/Base64#URL_applications


revLookup['-'.charCodeAt(0)] = 62;
revLookup['_'.charCodeAt(0)] = 63;

function getLens(b64) {
  var len = b64.length;

  if (len % 4 > 0) {
    throw new Error('Invalid string. Length must be a multiple of 4');
  } // Trim off extra bytes after placeholder bytes are found
  // See: https://github.com/beatgammit/base64-js/issues/42


  var validLen = b64.indexOf('=');
  if (validLen === -1) validLen = len;
  var placeHoldersLen = validLen === len ? 0 : 4 - validLen % 4;
  return [validLen, placeHoldersLen];
} // base64 is 4/3 + up to two characters of the original data


function byteLength(b64) {
  var lens = getLens(b64);
  var validLen = lens[0];
  var placeHoldersLen = lens[1];
  return (validLen + placeHoldersLen) * 3 / 4 - placeHoldersLen;
}

function _byteLength(b64, validLen, placeHoldersLen) {
  return (validLen + placeHoldersLen) * 3 / 4 - placeHoldersLen;
}

function toByteArray(b64) {
  var tmp;
  var lens = getLens(b64);
  var validLen = lens[0];
  var placeHoldersLen = lens[1];
  var arr = new Arr(_byteLength(b64, validLen, placeHoldersLen));
  var curByte = 0; // if there are placeholders, only get up to the last complete 4 chars

  var len = placeHoldersLen > 0 ? validLen - 4 : validLen;
  var i;

  for (i = 0; i < len; i += 4) {
    tmp = revLookup[b64.charCodeAt(i)] << 18 | revLookup[b64.charCodeAt(i + 1)] << 12 | revLookup[b64.charCodeAt(i + 2)] << 6 | revLookup[b64.charCodeAt(i + 3)];
    arr[curByte++] = tmp >> 16 & 0xFF;
    arr[curByte++] = tmp >> 8 & 0xFF;
    arr[curByte++] = tmp & 0xFF;
  }

  if (placeHoldersLen === 2) {
    tmp = revLookup[b64.charCodeAt(i)] << 2 | revLookup[b64.charCodeAt(i + 1)] >> 4;
    arr[curByte++] = tmp & 0xFF;
  }

  if (placeHoldersLen === 1) {
    tmp = revLookup[b64.charCodeAt(i)] << 10 | revLookup[b64.charCodeAt(i + 1)] << 4 | revLookup[b64.charCodeAt(i + 2)] >> 2;
    arr[curByte++] = tmp >> 8 & 0xFF;
    arr[curByte++] = tmp & 0xFF;
  }

  return arr;
}

function tripletToBase64(num) {
  return lookup[num >> 18 & 0x3F] + lookup[num >> 12 & 0x3F] + lookup[num >> 6 & 0x3F] + lookup[num & 0x3F];
}

function encodeChunk(uint8, start, end) {
  var tmp;
  var output = [];

  for (var i = start; i < end; i += 3) {
    tmp = (uint8[i] << 16 & 0xFF0000) + (uint8[i + 1] << 8 & 0xFF00) + (uint8[i + 2] & 0xFF);
    output.push(tripletToBase64(tmp));
  }

  return output.join('');
}

function fromByteArray(uint8) {
  var tmp;
  var len = uint8.length;
  var extraBytes = len % 3; // if we have 1 byte left, pad 2 bytes

  var parts = [];
  var maxChunkLength = 16383; // must be multiple of 3
  // go through the array every three bytes, we'll deal with trailing stuff later

  for (var i = 0, len2 = len - extraBytes; i < len2; i += maxChunkLength) {
    parts.push(encodeChunk(uint8, i, i + maxChunkLength > len2 ? len2 : i + maxChunkLength));
  } // pad the end with zeros, but make sure to not forget the extra bytes


  if (extraBytes === 1) {
    tmp = uint8[len - 1];
    parts.push(lookup[tmp >> 2] + lookup[tmp << 4 & 0x3F] + '==');
  } else if (extraBytes === 2) {
    tmp = (uint8[len - 2] << 8) + uint8[len - 1];
    parts.push(lookup[tmp >> 10] + lookup[tmp >> 4 & 0x3F] + lookup[tmp << 2 & 0x3F] + '=');
  }

  return parts.join('');
}

/***/ }),
/* 504 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(28);

__webpack_require__(46);

__webpack_require__(61);

__webpack_require__(32);

__webpack_require__(62);

__webpack_require__(134);

__webpack_require__(63);

__webpack_require__(64);

__webpack_require__(65);

__webpack_require__(66);

__webpack_require__(67);

__webpack_require__(68);

__webpack_require__(69);

__webpack_require__(70);

__webpack_require__(71);

__webpack_require__(72);

__webpack_require__(73);

__webpack_require__(74);

__webpack_require__(75);

__webpack_require__(76);

__webpack_require__(77);

__webpack_require__(78);

__webpack_require__(79);

__webpack_require__(80);

__webpack_require__(81);

__webpack_require__(82);

__webpack_require__(83);

__webpack_require__(84);

__webpack_require__(85);

// Generated by CoffeeScript 1.7.1
var UnicodeTrie, inflate;
inflate = __webpack_require__(135);

UnicodeTrie = function () {
  var DATA_BLOCK_LENGTH, DATA_GRANULARITY, DATA_MASK, INDEX_1_OFFSET, INDEX_2_BLOCK_LENGTH, INDEX_2_BMP_LENGTH, INDEX_2_MASK, INDEX_SHIFT, LSCP_INDEX_2_LENGTH, LSCP_INDEX_2_OFFSET, OMITTED_BMP_INDEX_1_LENGTH, SHIFT_1, SHIFT_1_2, SHIFT_2, UTF8_2B_INDEX_2_LENGTH, UTF8_2B_INDEX_2_OFFSET;
  SHIFT_1 = 6 + 5;
  SHIFT_2 = 5;
  SHIFT_1_2 = SHIFT_1 - SHIFT_2;
  OMITTED_BMP_INDEX_1_LENGTH = 0x10000 >> SHIFT_1;
  INDEX_2_BLOCK_LENGTH = 1 << SHIFT_1_2;
  INDEX_2_MASK = INDEX_2_BLOCK_LENGTH - 1;
  INDEX_SHIFT = 2;
  DATA_BLOCK_LENGTH = 1 << SHIFT_2;
  DATA_MASK = DATA_BLOCK_LENGTH - 1;
  LSCP_INDEX_2_OFFSET = 0x10000 >> SHIFT_2;
  LSCP_INDEX_2_LENGTH = 0x400 >> SHIFT_2;
  INDEX_2_BMP_LENGTH = LSCP_INDEX_2_OFFSET + LSCP_INDEX_2_LENGTH;
  UTF8_2B_INDEX_2_OFFSET = INDEX_2_BMP_LENGTH;
  UTF8_2B_INDEX_2_LENGTH = 0x800 >> 6;
  INDEX_1_OFFSET = UTF8_2B_INDEX_2_OFFSET + UTF8_2B_INDEX_2_LENGTH;
  DATA_GRANULARITY = 1 << INDEX_SHIFT;

  function UnicodeTrie(data) {
    var isBuffer, uncompressedLength, view;
    isBuffer = typeof data.readUInt32BE === 'function' && typeof data.slice === 'function';

    if (isBuffer || data instanceof Uint8Array) {
      if (isBuffer) {
        this.highStart = data.readUInt32BE(0);
        this.errorValue = data.readUInt32BE(4);
        uncompressedLength = data.readUInt32BE(8);
        data = data.slice(12);
      } else {
        view = new DataView(data.buffer);
        this.highStart = view.getUint32(0);
        this.errorValue = view.getUint32(4);
        uncompressedLength = view.getUint32(8);
        data = data.subarray(12);
      }

      data = inflate(data, new Uint8Array(uncompressedLength));
      data = inflate(data, new Uint8Array(uncompressedLength));
      this.data = new Uint32Array(data.buffer);
    } else {
      this.data = data.data, this.highStart = data.highStart, this.errorValue = data.errorValue;
    }
  }

  UnicodeTrie.prototype.get = function (codePoint) {
    var index;

    if (codePoint < 0 || codePoint > 0x10ffff) {
      return this.errorValue;
    }

    if (codePoint < 0xd800 || codePoint > 0xdbff && codePoint <= 0xffff) {
      index = (this.data[codePoint >> SHIFT_2] << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }

    if (codePoint <= 0xffff) {
      index = (this.data[LSCP_INDEX_2_OFFSET + (codePoint - 0xd800 >> SHIFT_2)] << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }

    if (codePoint < this.highStart) {
      index = this.data[INDEX_1_OFFSET - OMITTED_BMP_INDEX_1_LENGTH + (codePoint >> SHIFT_1)];
      index = this.data[index + (codePoint >> SHIFT_2 & INDEX_2_MASK)];
      index = (index << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }

    return this.data[this.data.length - DATA_GRANULARITY];
  };

  return UnicodeTrie;
}();

module.exports = UnicodeTrie;

/***/ }),
/* 505 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(197);

__webpack_require__(207);

__webpack_require__(208);

__webpack_require__(213);

__webpack_require__(28);

__webpack_require__(46);

__webpack_require__(157);

__webpack_require__(32);

__webpack_require__(118);

__webpack_require__(229);

__webpack_require__(159);

__webpack_require__(160);

__webpack_require__(120);

__webpack_require__(506);

function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } it = o[Symbol.iterator](); return it.next.bind(it); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

var INITIAL_STATE = 1;
var FAIL_STATE = 0;
/**
 * A StateMachine represents a deterministic finite automaton.
 * It can perform matches over a sequence of values, similar to a regular expression.
 */

var StateMachine = /*#__PURE__*/function () {
  function StateMachine(dfa) {
    this.stateTable = dfa.stateTable;
    this.accepting = dfa.accepting;
    this.tags = dfa.tags;
  }
  /**
   * Returns an iterable object that yields pattern matches over the input sequence.
   * Matches are of the form [startIndex, endIndex, tags].
   */


  var _proto = StateMachine.prototype;

  _proto.match = function match(str) {
    var _ref;

    var self = this;
    return _ref = {}, _ref[Symbol.iterator] = /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
      var state, startRun, lastAccepting, lastState, p, c;
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              state = INITIAL_STATE;
              startRun = null;
              lastAccepting = null;
              lastState = null;
              p = 0;

            case 5:
              if (!(p < str.length)) {
                _context.next = 21;
                break;
              }

              c = str[p];
              lastState = state;
              state = self.stateTable[state][c];

              if (!(state === FAIL_STATE)) {
                _context.next = 15;
                break;
              }

              if (!(startRun != null && lastAccepting != null && lastAccepting >= startRun)) {
                _context.next = 13;
                break;
              }

              _context.next = 13;
              return [startRun, lastAccepting, self.tags[lastState]];

            case 13:
              // reset the state as if we started over from the initial state
              state = self.stateTable[INITIAL_STATE][c];
              startRun = null;

            case 15:
              // start a run if not in the failure state
              if (state !== FAIL_STATE && startRun == null) {
                startRun = p;
              } // if accepting, mark the potential match end


              if (self.accepting[state]) {
                lastAccepting = p;
              } // reset the state to the initial state if we get into the failure state


              if (state === FAIL_STATE) {
                state = INITIAL_STATE;
              }

            case 18:
              p++;
              _context.next = 5;
              break;

            case 21:
              if (!(startRun != null && lastAccepting != null && lastAccepting >= startRun)) {
                _context.next = 24;
                break;
              }

              _context.next = 24;
              return [startRun, lastAccepting, self.tags[state]];

            case 24:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }), _ref;
  }
  /**
   * For each match over the input sequence, action functions matching
   * the tag definitions in the input pattern are called with the startIndex,
   * endIndex, and sub-match sequence.
   */
  ;

  _proto.apply = function apply(str, actions) {
    for (var _iterator = _createForOfIteratorHelperLoose(this.match(str)), _step; !(_step = _iterator()).done;) {
      var _step$value = _step.value,
          start = _step$value[0],
          end = _step$value[1],
          tags = _step$value[2];

      for (var _iterator2 = _createForOfIteratorHelperLoose(tags), _step2; !(_step2 = _iterator2()).done;) {
        var tag = _step2.value;

        if (typeof actions[tag] === 'function') {
          actions[tag](start, end, str.slice(start, end + 1));
        }
      }
    }
  };

  return StateMachine;
}();

module.exports = StateMachine;

/***/ }),
/* 506 */
/***/ (function(module, exports) {

/**
 * Copyright (c) 2014-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

!(function(global) {
  "use strict";

  var Op = Object.prototype;
  var hasOwn = Op.hasOwnProperty;
  var undefined; // More compressible than void 0.
  var $Symbol = typeof Symbol === "function" ? Symbol : {};
  var iteratorSymbol = $Symbol.iterator || "@@iterator";
  var asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator";
  var toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  var inModule = typeof module === "object";
  var runtime = global.regeneratorRuntime;
  if (runtime) {
    if (inModule) {
      // If regeneratorRuntime is defined globally and we're in a module,
      // make the exports object identical to regeneratorRuntime.
      module.exports = runtime;
    }
    // Don't bother evaluating the rest of this file if the runtime was
    // already defined globally.
    return;
  }

  // Define the runtime globally (as expected by generated code) as either
  // module.exports (if we're in a module) or a new, empty object.
  runtime = global.regeneratorRuntime = inModule ? module.exports : {};

  function wrap(innerFn, outerFn, self, tryLocsList) {
    // If outerFn provided and outerFn.prototype is a Generator, then outerFn.prototype instanceof Generator.
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator;
    var generator = Object.create(protoGenerator.prototype);
    var context = new Context(tryLocsList || []);

    // The ._invoke method unifies the implementations of the .next,
    // .throw, and .return methods.
    generator._invoke = makeInvokeMethod(innerFn, self, context);

    return generator;
  }
  runtime.wrap = wrap;

  // Try/catch helper to minimize deoptimizations. Returns a completion
  // record like context.tryEntries[i].completion. This interface could
  // have been (and was previously) designed to take a closure to be
  // invoked without arguments, but in all the cases we care about we
  // already have an existing method we want to call, so there's no need
  // to create a new function object. We can even get away with assuming
  // the method takes exactly one argument, since that happens to be true
  // in every case, so we don't have to touch the arguments object. The
  // only additional allocation required is the completion record, which
  // has a stable shape and so hopefully should be cheap to allocate.
  function tryCatch(fn, obj, arg) {
    try {
      return { type: "normal", arg: fn.call(obj, arg) };
    } catch (err) {
      return { type: "throw", arg: err };
    }
  }

  var GenStateSuspendedStart = "suspendedStart";
  var GenStateSuspendedYield = "suspendedYield";
  var GenStateExecuting = "executing";
  var GenStateCompleted = "completed";

  // Returning this object from the innerFn has the same effect as
  // breaking out of the dispatch switch statement.
  var ContinueSentinel = {};

  // Dummy constructor functions that we use as the .constructor and
  // .constructor.prototype properties for functions that return Generator
  // objects. For full spec compliance, you may wish to configure your
  // minifier not to mangle the names of these two functions.
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}

  // This is a polyfill for %IteratorPrototype% for environments that
  // don't natively support it.
  var IteratorPrototype = {};
  IteratorPrototype[iteratorSymbol] = function () {
    return this;
  };

  var getProto = Object.getPrototypeOf;
  var NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  if (NativeIteratorPrototype &&
      NativeIteratorPrototype !== Op &&
      hasOwn.call(NativeIteratorPrototype, iteratorSymbol)) {
    // This environment has a native %IteratorPrototype%; use it instead
    // of the polyfill.
    IteratorPrototype = NativeIteratorPrototype;
  }

  var Gp = GeneratorFunctionPrototype.prototype =
    Generator.prototype = Object.create(IteratorPrototype);
  GeneratorFunction.prototype = Gp.constructor = GeneratorFunctionPrototype;
  GeneratorFunctionPrototype.constructor = GeneratorFunction;
  GeneratorFunctionPrototype[toStringTagSymbol] =
    GeneratorFunction.displayName = "GeneratorFunction";

  // Helper for defining the .next, .throw, and .return methods of the
  // Iterator interface in terms of a single ._invoke method.
  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function(method) {
      prototype[method] = function(arg) {
        return this._invoke(method, arg);
      };
    });
  }

  runtime.isGeneratorFunction = function(genFun) {
    var ctor = typeof genFun === "function" && genFun.constructor;
    return ctor
      ? ctor === GeneratorFunction ||
        // For the native GeneratorFunction constructor, the best we can
        // do is to check its .name property.
        (ctor.displayName || ctor.name) === "GeneratorFunction"
      : false;
  };

  runtime.mark = function(genFun) {
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(genFun, GeneratorFunctionPrototype);
    } else {
      genFun.__proto__ = GeneratorFunctionPrototype;
      if (!(toStringTagSymbol in genFun)) {
        genFun[toStringTagSymbol] = "GeneratorFunction";
      }
    }
    genFun.prototype = Object.create(Gp);
    return genFun;
  };

  // Within the body of any async function, `await x` is transformed to
  // `yield regeneratorRuntime.awrap(x)`, so that the runtime can test
  // `hasOwn.call(value, "__await")` to determine if the yielded value is
  // meant to be awaited.
  runtime.awrap = function(arg) {
    return { __await: arg };
  };

  function AsyncIterator(generator) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);
      if (record.type === "throw") {
        reject(record.arg);
      } else {
        var result = record.arg;
        var value = result.value;
        if (value &&
            typeof value === "object" &&
            hasOwn.call(value, "__await")) {
          return Promise.resolve(value.__await).then(function(value) {
            invoke("next", value, resolve, reject);
          }, function(err) {
            invoke("throw", err, resolve, reject);
          });
        }

        return Promise.resolve(value).then(function(unwrapped) {
          // When a yielded Promise is resolved, its final value becomes
          // the .value of the Promise<{value,done}> result for the
          // current iteration. If the Promise is rejected, however, the
          // result for this iteration will be rejected with the same
          // reason. Note that rejections of yielded Promises are not
          // thrown back into the generator function, as is the case
          // when an awaited Promise is rejected. This difference in
          // behavior between yield and await is important, because it
          // allows the consumer to decide what to do with the yielded
          // rejection (swallow it and continue, manually .throw it back
          // into the generator, abandon iteration, whatever). With
          // await, by contrast, there is no opportunity to examine the
          // rejection reason outside the generator function, so the
          // only option is to throw it from the await expression, and
          // let the generator function handle the exception.
          result.value = unwrapped;
          resolve(result);
        }, reject);
      }
    }

    var previousPromise;

    function enqueue(method, arg) {
      function callInvokeWithMethodAndArg() {
        return new Promise(function(resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise =
        // If enqueue has been called before, then we want to wait until
        // all previous Promises have been resolved before calling invoke,
        // so that results are always delivered in the correct order. If
        // enqueue has not been called before, then it is important to
        // call invoke immediately, without waiting on a callback to fire,
        // so that the async generator function has the opportunity to do
        // any necessary setup in a predictable way. This predictability
        // is why the Promise constructor synchronously invokes its
        // executor callback, and why async functions synchronously
        // execute code before the first await. Since we implement simple
        // async functions in terms of async generators, it is especially
        // important to get this right, even though it requires care.
        previousPromise ? previousPromise.then(
          callInvokeWithMethodAndArg,
          // Avoid propagating failures to Promises returned by later
          // invocations of the iterator.
          callInvokeWithMethodAndArg
        ) : callInvokeWithMethodAndArg();
    }

    // Define the unified helper method that is used to implement .next,
    // .throw, and .return (see defineIteratorMethods).
    this._invoke = enqueue;
  }

  defineIteratorMethods(AsyncIterator.prototype);
  AsyncIterator.prototype[asyncIteratorSymbol] = function () {
    return this;
  };
  runtime.AsyncIterator = AsyncIterator;

  // Note that simple async functions are implemented on top of
  // AsyncIterator objects; they just return a Promise for the value of
  // the final result produced by the iterator.
  runtime.async = function(innerFn, outerFn, self, tryLocsList) {
    var iter = new AsyncIterator(
      wrap(innerFn, outerFn, self, tryLocsList)
    );

    return runtime.isGeneratorFunction(outerFn)
      ? iter // If outerFn is a generator, return the full iterator.
      : iter.next().then(function(result) {
          return result.done ? result.value : iter.next();
        });
  };

  function makeInvokeMethod(innerFn, self, context) {
    var state = GenStateSuspendedStart;

    return function invoke(method, arg) {
      if (state === GenStateExecuting) {
        throw new Error("Generator is already running");
      }

      if (state === GenStateCompleted) {
        if (method === "throw") {
          throw arg;
        }

        // Be forgiving, per 25.3.3.3.3 of the spec:
        // https://people.mozilla.org/~jorendorff/es6-draft.html#sec-generatorresume
        return doneResult();
      }

      context.method = method;
      context.arg = arg;

      while (true) {
        var delegate = context.delegate;
        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);
          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }

        if (context.method === "next") {
          // Setting context._sent for legacy support of Babel's
          // function.sent implementation.
          context.sent = context._sent = context.arg;

        } else if (context.method === "throw") {
          if (state === GenStateSuspendedStart) {
            state = GenStateCompleted;
            throw context.arg;
          }

          context.dispatchException(context.arg);

        } else if (context.method === "return") {
          context.abrupt("return", context.arg);
        }

        state = GenStateExecuting;

        var record = tryCatch(innerFn, self, context);
        if (record.type === "normal") {
          // If an exception is thrown from innerFn, we leave state ===
          // GenStateExecuting and loop back for another invocation.
          state = context.done
            ? GenStateCompleted
            : GenStateSuspendedYield;

          if (record.arg === ContinueSentinel) {
            continue;
          }

          return {
            value: record.arg,
            done: context.done
          };

        } else if (record.type === "throw") {
          state = GenStateCompleted;
          // Dispatch the exception by looping back around to the
          // context.dispatchException(context.arg) call above.
          context.method = "throw";
          context.arg = record.arg;
        }
      }
    };
  }

  // Call delegate.iterator[context.method](context.arg) and handle the
  // result, either by returning a { value, done } result from the
  // delegate iterator, or by modifying context.method and context.arg,
  // setting context.delegate to null, and returning the ContinueSentinel.
  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];
    if (method === undefined) {
      // A .throw or .return when the delegate iterator has no .throw
      // method always terminates the yield* loop.
      context.delegate = null;

      if (context.method === "throw") {
        if (delegate.iterator.return) {
          // If the delegate iterator has a return method, give it a
          // chance to clean up.
          context.method = "return";
          context.arg = undefined;
          maybeInvokeDelegate(delegate, context);

          if (context.method === "throw") {
            // If maybeInvokeDelegate(context) changed context.method from
            // "return" to "throw", let that override the TypeError below.
            return ContinueSentinel;
          }
        }

        context.method = "throw";
        context.arg = new TypeError(
          "The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);

    if (record.type === "throw") {
      context.method = "throw";
      context.arg = record.arg;
      context.delegate = null;
      return ContinueSentinel;
    }

    var info = record.arg;

    if (! info) {
      context.method = "throw";
      context.arg = new TypeError("iterator result is not an object");
      context.delegate = null;
      return ContinueSentinel;
    }

    if (info.done) {
      // Assign the result of the finished delegate to the temporary
      // variable specified by delegate.resultName (see delegateYield).
      context[delegate.resultName] = info.value;

      // Resume execution at the desired location (see delegateYield).
      context.next = delegate.nextLoc;

      // If context.method was "throw" but the delegate handled the
      // exception, let the outer generator proceed normally. If
      // context.method was "next", forget context.arg since it has been
      // "consumed" by the delegate iterator. If context.method was
      // "return", allow the original .return call to continue in the
      // outer generator.
      if (context.method !== "return") {
        context.method = "next";
        context.arg = undefined;
      }

    } else {
      // Re-yield the result returned by the delegate method.
      return info;
    }

    // The delegate iterator is finished, so forget it and continue with
    // the outer generator.
    context.delegate = null;
    return ContinueSentinel;
  }

  // Define Generator.prototype.{next,throw,return} in terms of the
  // unified ._invoke helper method.
  defineIteratorMethods(Gp);

  Gp[toStringTagSymbol] = "Generator";

  // A Generator should always return itself as the iterator object when the
  // @@iterator function is called on it. Some browsers' implementations of the
  // iterator prototype chain incorrectly implement this, causing the Generator
  // object to not be returned from this call. This ensures that doesn't happen.
  // See https://github.com/facebook/regenerator/issues/274 for more details.
  Gp[iteratorSymbol] = function() {
    return this;
  };

  Gp.toString = function() {
    return "[object Generator]";
  };

  function pushTryEntry(locs) {
    var entry = { tryLoc: locs[0] };

    if (1 in locs) {
      entry.catchLoc = locs[1];
    }

    if (2 in locs) {
      entry.finallyLoc = locs[2];
      entry.afterLoc = locs[3];
    }

    this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal";
    delete record.arg;
    entry.completion = record;
  }

  function Context(tryLocsList) {
    // The root entry object (effectively a try statement without a catch
    // or a finally block) gives us a place to store values thrown from
    // locations where there is no enclosing try statement.
    this.tryEntries = [{ tryLoc: "root" }];
    tryLocsList.forEach(pushTryEntry, this);
    this.reset(true);
  }

  runtime.keys = function(object) {
    var keys = [];
    for (var key in object) {
      keys.push(key);
    }
    keys.reverse();

    // Rather than returning an object with a next method, we keep
    // things simple and return the next function itself.
    return function next() {
      while (keys.length) {
        var key = keys.pop();
        if (key in object) {
          next.value = key;
          next.done = false;
          return next;
        }
      }

      // To avoid creating an additional object, we just hang the .value
      // and .done properties off the next function object itself. This
      // also ensures that the minifier will not anonymize the function.
      next.done = true;
      return next;
    };
  };

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) {
        return iteratorMethod.call(iterable);
      }

      if (typeof iterable.next === "function") {
        return iterable;
      }

      if (!isNaN(iterable.length)) {
        var i = -1, next = function next() {
          while (++i < iterable.length) {
            if (hasOwn.call(iterable, i)) {
              next.value = iterable[i];
              next.done = false;
              return next;
            }
          }

          next.value = undefined;
          next.done = true;

          return next;
        };

        return next.next = next;
      }
    }

    // Return an iterator with no values.
    return { next: doneResult };
  }
  runtime.values = values;

  function doneResult() {
    return { value: undefined, done: true };
  }

  Context.prototype = {
    constructor: Context,

    reset: function(skipTempReset) {
      this.prev = 0;
      this.next = 0;
      // Resetting context._sent for legacy support of Babel's
      // function.sent implementation.
      this.sent = this._sent = undefined;
      this.done = false;
      this.delegate = null;

      this.method = "next";
      this.arg = undefined;

      this.tryEntries.forEach(resetTryEntry);

      if (!skipTempReset) {
        for (var name in this) {
          // Not sure about the optimal order of these conditions:
          if (name.charAt(0) === "t" &&
              hasOwn.call(this, name) &&
              !isNaN(+name.slice(1))) {
            this[name] = undefined;
          }
        }
      }
    },

    stop: function() {
      this.done = true;

      var rootEntry = this.tryEntries[0];
      var rootRecord = rootEntry.completion;
      if (rootRecord.type === "throw") {
        throw rootRecord.arg;
      }

      return this.rval;
    },

    dispatchException: function(exception) {
      if (this.done) {
        throw exception;
      }

      var context = this;
      function handle(loc, caught) {
        record.type = "throw";
        record.arg = exception;
        context.next = loc;

        if (caught) {
          // If the dispatched exception was caught by a catch block,
          // then let that catch block handle the exception normally.
          context.method = "next";
          context.arg = undefined;
        }

        return !! caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        var record = entry.completion;

        if (entry.tryLoc === "root") {
          // Exception thrown outside of any try block that could handle
          // it, so set the completion value of the entire function to
          // throw the exception.
          return handle("end");
        }

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc");
          var hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            } else if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            }

          } else if (hasFinally) {
            if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else {
            throw new Error("try statement without catch or finally");
          }
        }
      }
    },

    abrupt: function(type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc <= this.prev &&
            hasOwn.call(entry, "finallyLoc") &&
            this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      if (finallyEntry &&
          (type === "break" ||
           type === "continue") &&
          finallyEntry.tryLoc <= arg &&
          arg <= finallyEntry.finallyLoc) {
        // Ignore the finally entry if control is not jumping to a
        // location outside the try/catch block.
        finallyEntry = null;
      }

      var record = finallyEntry ? finallyEntry.completion : {};
      record.type = type;
      record.arg = arg;

      if (finallyEntry) {
        this.method = "next";
        this.next = finallyEntry.finallyLoc;
        return ContinueSentinel;
      }

      return this.complete(record);
    },

    complete: function(record, afterLoc) {
      if (record.type === "throw") {
        throw record.arg;
      }

      if (record.type === "break" ||
          record.type === "continue") {
        this.next = record.arg;
      } else if (record.type === "return") {
        this.rval = this.arg = record.arg;
        this.method = "return";
        this.next = "end";
      } else if (record.type === "normal" && afterLoc) {
        this.next = afterLoc;
      }

      return ContinueSentinel;
    },

    finish: function(finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) {
          this.complete(entry.completion, entry.afterLoc);
          resetTryEntry(entry);
          return ContinueSentinel;
        }
      }
    },

    "catch": function(tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;
          if (record.type === "throw") {
            var thrown = record.arg;
            resetTryEntry(entry);
          }
          return thrown;
        }
      }

      // The context.catch method must only be called with a location
      // argument that corresponds to a known catch block.
      throw new Error("illegal catch attempt");
    },

    delegateYield: function(iterable, resultName, nextLoc) {
      this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      };

      if (this.method === "next") {
        // Deliberately forget the last sent value so that we don't
        // accidentally pass it on to the delegate.
        this.arg = undefined;
      }

      return ContinueSentinel;
    }
  };
})(
  // In sloppy mode, unbound `this` refers to the global object, fallback to
  // Function constructor if we're in global strict mode. That is sadly a form
  // of indirect eval which violates Content Security Policy.
  (function() { return this })() || Function("return this")()
);


/***/ }),
/* 507 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(508), __esModule: true };

/***/ }),
/* 508 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(509);
module.exports = Math.pow(2, -52);


/***/ }),
/* 509 */
/***/ (function(module, exports, __webpack_require__) {

// 20.1.2.1 Number.EPSILON
var $export = __webpack_require__(9);

$export($export.S, 'Number', { EPSILON: Math.pow(2, -52) });


/***/ }),
/* 510 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(Buffer) {var clone = (function() {
'use strict';

/**
 * Clones (copies) an Object using deep copying.
 *
 * This function supports circular references by default, but if you are certain
 * there are no circular references in your object, you can save some CPU time
 * by calling clone(obj, false).
 *
 * Caution: if `circular` is false and `parent` contains circular references,
 * your program may enter an infinite loop and crash.
 *
 * @param `parent` - the object to be cloned
 * @param `circular` - set to true if the object to be cloned may contain
 *    circular references. (optional - true by default)
 * @param `depth` - set to a number if the object is only to be cloned to
 *    a particular depth. (optional - defaults to Infinity)
 * @param `prototype` - sets the prototype to be used when cloning an object.
 *    (optional - defaults to parent prototype).
*/
function clone(parent, circular, depth, prototype) {
  var filter;
  if (typeof circular === 'object') {
    depth = circular.depth;
    prototype = circular.prototype;
    filter = circular.filter;
    circular = circular.circular
  }
  // maintain two arrays for circular references, where corresponding parents
  // and children have the same index
  var allParents = [];
  var allChildren = [];

  var useBuffer = typeof Buffer != 'undefined';

  if (typeof circular == 'undefined')
    circular = true;

  if (typeof depth == 'undefined')
    depth = Infinity;

  // recurse this function so we don't reset allParents and allChildren
  function _clone(parent, depth) {
    // cloning null always returns null
    if (parent === null)
      return null;

    if (depth == 0)
      return parent;

    var child;
    var proto;
    if (typeof parent != 'object') {
      return parent;
    }

    if (clone.__isArray(parent)) {
      child = [];
    } else if (clone.__isRegExp(parent)) {
      child = new RegExp(parent.source, __getRegExpFlags(parent));
      if (parent.lastIndex) child.lastIndex = parent.lastIndex;
    } else if (clone.__isDate(parent)) {
      child = new Date(parent.getTime());
    } else if (useBuffer && Buffer.isBuffer(parent)) {
      if (Buffer.allocUnsafe) {
        // Node.js >= 4.5.0
        child = Buffer.allocUnsafe(parent.length);
      } else {
        // Older Node.js versions
        child = new Buffer(parent.length);
      }
      parent.copy(child);
      return child;
    } else {
      if (typeof prototype == 'undefined') {
        proto = Object.getPrototypeOf(parent);
        child = Object.create(proto);
      }
      else {
        child = Object.create(prototype);
        proto = prototype;
      }
    }

    if (circular) {
      var index = allParents.indexOf(parent);

      if (index != -1) {
        return allChildren[index];
      }
      allParents.push(parent);
      allChildren.push(child);
    }

    for (var i in parent) {
      var attrs;
      if (proto) {
        attrs = Object.getOwnPropertyDescriptor(proto, i);
      }

      if (attrs && attrs.set == null) {
        continue;
      }
      child[i] = _clone(parent[i], depth - 1);
    }

    return child;
  }

  return _clone(parent, depth);
}

/**
 * Simple flat clone using prototype, accepts only objects, usefull for property
 * override on FLAT configuration object (no nested props).
 *
 * USE WITH CAUTION! This may not behave as you wish if you do not know how this
 * works.
 */
clone.clonePrototype = function clonePrototype(parent) {
  if (parent === null)
    return null;

  var c = function () {};
  c.prototype = parent;
  return new c();
};

// private utility functions

function __objToStr(o) {
  return Object.prototype.toString.call(o);
};
clone.__objToStr = __objToStr;

function __isDate(o) {
  return typeof o === 'object' && __objToStr(o) === '[object Date]';
};
clone.__isDate = __isDate;

function __isArray(o) {
  return typeof o === 'object' && __objToStr(o) === '[object Array]';
};
clone.__isArray = __isArray;

function __isRegExp(o) {
  return typeof o === 'object' && __objToStr(o) === '[object RegExp]';
};
clone.__isRegExp = __isRegExp;

function __getRegExpFlags(re) {
  var flags = '';
  if (re.global) flags += 'g';
  if (re.ignoreCase) flags += 'i';
  if (re.multiline) flags += 'm';
  return flags;
};
clone.__getRegExpFlags = __getRegExpFlags;

return clone;
})();

if ( true && module.exports) {
  module.exports = clone;
}

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer))

/***/ }),
/* 511 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = { "default": __webpack_require__(512), __esModule: true };

/***/ }),
/* 512 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(133);
__webpack_require__(91);
__webpack_require__(102);
__webpack_require__(513);
__webpack_require__(517);
__webpack_require__(518);
module.exports = __webpack_require__(7).Promise;


/***/ }),
/* 513 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var LIBRARY = __webpack_require__(104);
var global = __webpack_require__(17);
var ctx = __webpack_require__(33);
var classof = __webpack_require__(184);
var $export = __webpack_require__(9);
var isObject = __webpack_require__(22);
var aFunction = __webpack_require__(101);
var anInstance = __webpack_require__(190);
var forOf = __webpack_require__(107);
var speciesConstructor = __webpack_require__(282);
var task = __webpack_require__(283).set;
var microtask = __webpack_require__(515)();
var newPromiseCapabilityModule = __webpack_require__(193);
var perform = __webpack_require__(284);
var userAgent = __webpack_require__(516);
var promiseResolve = __webpack_require__(285);
var PROMISE = 'Promise';
var TypeError = global.TypeError;
var process = global.process;
var versions = process && process.versions;
var v8 = versions && versions.v8 || '';
var $Promise = global[PROMISE];
var isNode = classof(process) == 'process';
var empty = function () { /* empty */ };
var Internal, newGenericPromiseCapability, OwnPromiseCapability, Wrapper;
var newPromiseCapability = newGenericPromiseCapability = newPromiseCapabilityModule.f;

var USE_NATIVE = !!function () {
  try {
    // correct subclassing with @@species support
    var promise = $Promise.resolve(1);
    var FakePromise = (promise.constructor = {})[__webpack_require__(21)('species')] = function (exec) {
      exec(empty, empty);
    };
    // unhandled rejections tracking support, NodeJS Promise without it fails @@species test
    return (isNode || typeof PromiseRejectionEvent == 'function')
      && promise.then(empty) instanceof FakePromise
      // v8 6.6 (Node 10 and Chrome 66) have a bug with resolving custom thenables
      // https://bugs.chromium.org/p/chromium/issues/detail?id=830565
      // we can't detect it synchronously, so just check versions
      && v8.indexOf('6.6') !== 0
      && userAgent.indexOf('Chrome/66') === -1;
  } catch (e) { /* empty */ }
}();

// helpers
var isThenable = function (it) {
  var then;
  return isObject(it) && typeof (then = it.then) == 'function' ? then : false;
};
var notify = function (promise, isReject) {
  if (promise._n) return;
  promise._n = true;
  var chain = promise._c;
  microtask(function () {
    var value = promise._v;
    var ok = promise._s == 1;
    var i = 0;
    var run = function (reaction) {
      var handler = ok ? reaction.ok : reaction.fail;
      var resolve = reaction.resolve;
      var reject = reaction.reject;
      var domain = reaction.domain;
      var result, then, exited;
      try {
        if (handler) {
          if (!ok) {
            if (promise._h == 2) onHandleUnhandled(promise);
            promise._h = 1;
          }
          if (handler === true) result = value;
          else {
            if (domain) domain.enter();
            result = handler(value); // may throw
            if (domain) {
              domain.exit();
              exited = true;
            }
          }
          if (result === reaction.promise) {
            reject(TypeError('Promise-chain cycle'));
          } else if (then = isThenable(result)) {
            then.call(result, resolve, reject);
          } else resolve(result);
        } else reject(value);
      } catch (e) {
        if (domain && !exited) domain.exit();
        reject(e);
      }
    };
    while (chain.length > i) run(chain[i++]); // variable length - can't use forEach
    promise._c = [];
    promise._n = false;
    if (isReject && !promise._h) onUnhandled(promise);
  });
};
var onUnhandled = function (promise) {
  task.call(global, function () {
    var value = promise._v;
    var unhandled = isUnhandled(promise);
    var result, handler, console;
    if (unhandled) {
      result = perform(function () {
        if (isNode) {
          process.emit('unhandledRejection', value, promise);
        } else if (handler = global.onunhandledrejection) {
          handler({ promise: promise, reason: value });
        } else if ((console = global.console) && console.error) {
          console.error('Unhandled promise rejection', value);
        }
      });
      // Browsers should not trigger `rejectionHandled` event if it was handled here, NodeJS - should
      promise._h = isNode || isUnhandled(promise) ? 2 : 1;
    } promise._a = undefined;
    if (unhandled && result.e) throw result.v;
  });
};
var isUnhandled = function (promise) {
  return promise._h !== 1 && (promise._a || promise._c).length === 0;
};
var onHandleUnhandled = function (promise) {
  task.call(global, function () {
    var handler;
    if (isNode) {
      process.emit('rejectionHandled', promise);
    } else if (handler = global.onrejectionhandled) {
      handler({ promise: promise, reason: promise._v });
    }
  });
};
var $reject = function (value) {
  var promise = this;
  if (promise._d) return;
  promise._d = true;
  promise = promise._w || promise; // unwrap
  promise._v = value;
  promise._s = 2;
  if (!promise._a) promise._a = promise._c.slice();
  notify(promise, true);
};
var $resolve = function (value) {
  var promise = this;
  var then;
  if (promise._d) return;
  promise._d = true;
  promise = promise._w || promise; // unwrap
  try {
    if (promise === value) throw TypeError("Promise can't be resolved itself");
    if (then = isThenable(value)) {
      microtask(function () {
        var wrapper = { _w: promise, _d: false }; // wrap
        try {
          then.call(value, ctx($resolve, wrapper, 1), ctx($reject, wrapper, 1));
        } catch (e) {
          $reject.call(wrapper, e);
        }
      });
    } else {
      promise._v = value;
      promise._s = 1;
      notify(promise, false);
    }
  } catch (e) {
    $reject.call({ _w: promise, _d: false }, e); // wrap
  }
};

// constructor polyfill
if (!USE_NATIVE) {
  // 25.4.3.1 Promise(executor)
  $Promise = function Promise(executor) {
    anInstance(this, $Promise, PROMISE, '_h');
    aFunction(executor);
    Internal.call(this);
    try {
      executor(ctx($resolve, this, 1), ctx($reject, this, 1));
    } catch (err) {
      $reject.call(this, err);
    }
  };
  // eslint-disable-next-line no-unused-vars
  Internal = function Promise(executor) {
    this._c = [];             // <- awaiting reactions
    this._a = undefined;      // <- checked in isUnhandled reactions
    this._s = 0;              // <- state
    this._d = false;          // <- done
    this._v = undefined;      // <- value
    this._h = 0;              // <- rejection state, 0 - default, 1 - handled, 2 - unhandled
    this._n = false;          // <- notify
  };
  Internal.prototype = __webpack_require__(189)($Promise.prototype, {
    // 25.4.5.3 Promise.prototype.then(onFulfilled, onRejected)
    then: function then(onFulfilled, onRejected) {
      var reaction = newPromiseCapability(speciesConstructor(this, $Promise));
      reaction.ok = typeof onFulfilled == 'function' ? onFulfilled : true;
      reaction.fail = typeof onRejected == 'function' && onRejected;
      reaction.domain = isNode ? process.domain : undefined;
      this._c.push(reaction);
      if (this._a) this._a.push(reaction);
      if (this._s) notify(this, false);
      return reaction.promise;
    },
    // 25.4.5.1 Promise.prototype.catch(onRejected)
    'catch': function (onRejected) {
      return this.then(undefined, onRejected);
    }
  });
  OwnPromiseCapability = function () {
    var promise = new Internal();
    this.promise = promise;
    this.resolve = ctx($resolve, promise, 1);
    this.reject = ctx($reject, promise, 1);
  };
  newPromiseCapabilityModule.f = newPromiseCapability = function (C) {
    return C === $Promise || C === Wrapper
      ? new OwnPromiseCapability(C)
      : newGenericPromiseCapability(C);
  };
}

$export($export.G + $export.W + $export.F * !USE_NATIVE, { Promise: $Promise });
__webpack_require__(106)($Promise, PROMISE);
__webpack_require__(267)(PROMISE);
Wrapper = __webpack_require__(7)[PROMISE];

// statics
$export($export.S + $export.F * !USE_NATIVE, PROMISE, {
  // 25.4.4.5 Promise.reject(r)
  reject: function reject(r) {
    var capability = newPromiseCapability(this);
    var $$reject = capability.reject;
    $$reject(r);
    return capability.promise;
  }
});
$export($export.S + $export.F * (LIBRARY || !USE_NATIVE), PROMISE, {
  // 25.4.4.6 Promise.resolve(x)
  resolve: function resolve(x) {
    return promiseResolve(LIBRARY && this === Wrapper ? $Promise : this, x);
  }
});
$export($export.S + $export.F * !(USE_NATIVE && __webpack_require__(281)(function (iter) {
  $Promise.all(iter)['catch'](empty);
})), PROMISE, {
  // 25.4.4.1 Promise.all(iterable)
  all: function all(iterable) {
    var C = this;
    var capability = newPromiseCapability(C);
    var resolve = capability.resolve;
    var reject = capability.reject;
    var result = perform(function () {
      var values = [];
      var index = 0;
      var remaining = 1;
      forOf(iterable, false, function (promise) {
        var $index = index++;
        var alreadyCalled = false;
        values.push(undefined);
        remaining++;
        C.resolve(promise).then(function (value) {
          if (alreadyCalled) return;
          alreadyCalled = true;
          values[$index] = value;
          --remaining || resolve(values);
        }, reject);
      });
      --remaining || resolve(values);
    });
    if (result.e) reject(result.v);
    return capability.promise;
  },
  // 25.4.4.4 Promise.race(iterable)
  race: function race(iterable) {
    var C = this;
    var capability = newPromiseCapability(C);
    var reject = capability.reject;
    var result = perform(function () {
      forOf(iterable, false, function (promise) {
        C.resolve(promise).then(capability.resolve, reject);
      });
    });
    if (result.e) reject(result.v);
    return capability.promise;
  }
});


/***/ }),
/* 514 */
/***/ (function(module, exports) {

// fast apply, http://jsperf.lnkit.com/fast-apply/5
module.exports = function (fn, args, that) {
  var un = that === undefined;
  switch (args.length) {
    case 0: return un ? fn()
                      : fn.call(that);
    case 1: return un ? fn(args[0])
                      : fn.call(that, args[0]);
    case 2: return un ? fn(args[0], args[1])
                      : fn.call(that, args[0], args[1]);
    case 3: return un ? fn(args[0], args[1], args[2])
                      : fn.call(that, args[0], args[1], args[2]);
    case 4: return un ? fn(args[0], args[1], args[2], args[3])
                      : fn.call(that, args[0], args[1], args[2], args[3]);
  } return fn.apply(that, args);
};


/***/ }),
/* 515 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(17);
var macrotask = __webpack_require__(283).set;
var Observer = global.MutationObserver || global.WebKitMutationObserver;
var process = global.process;
var Promise = global.Promise;
var isNode = __webpack_require__(99)(process) == 'process';

module.exports = function () {
  var head, last, notify;

  var flush = function () {
    var parent, fn;
    if (isNode && (parent = process.domain)) parent.exit();
    while (head) {
      fn = head.fn;
      head = head.next;
      try {
        fn();
      } catch (e) {
        if (head) notify();
        else last = undefined;
        throw e;
      }
    } last = undefined;
    if (parent) parent.enter();
  };

  // Node.js
  if (isNode) {
    notify = function () {
      process.nextTick(flush);
    };
  // browsers with MutationObserver, except iOS Safari - https://github.com/zloirock/core-js/issues/339
  } else if (Observer && !(global.navigator && global.navigator.standalone)) {
    var toggle = true;
    var node = document.createTextNode('');
    new Observer(flush).observe(node, { characterData: true }); // eslint-disable-line no-new
    notify = function () {
      node.data = toggle = !toggle;
    };
  // environments with maybe non-completely correct, but existent Promise
  } else if (Promise && Promise.resolve) {
    // Promise.resolve without an argument throws an error in LG WebOS 2
    var promise = Promise.resolve(undefined);
    notify = function () {
      promise.then(flush);
    };
  // for other environments - macrotask based on:
  // - setImmediate
  // - MessageChannel
  // - window.postMessag
  // - onreadystatechange
  // - setTimeout
  } else {
    notify = function () {
      // strange IE + webpack dev server bug - use .call(global)
      macrotask.call(global, flush);
    };
  }

  return function (fn) {
    var task = { fn: fn, next: undefined };
    if (last) last.next = task;
    if (!head) {
      head = task;
      notify();
    } last = task;
  };
};


/***/ }),
/* 516 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(17);
var navigator = global.navigator;

module.exports = navigator && navigator.userAgent || '';


/***/ }),
/* 517 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
// https://github.com/tc39/proposal-promise-finally

var $export = __webpack_require__(9);
var core = __webpack_require__(7);
var global = __webpack_require__(17);
var speciesConstructor = __webpack_require__(282);
var promiseResolve = __webpack_require__(285);

$export($export.P + $export.R, 'Promise', { 'finally': function (onFinally) {
  var C = speciesConstructor(this, core.Promise || global.Promise);
  var isFunction = typeof onFinally == 'function';
  return this.then(
    isFunction ? function (x) {
      return promiseResolve(C, onFinally()).then(function () { return x; });
    } : onFinally,
    isFunction ? function (e) {
      return promiseResolve(C, onFinally()).then(function () { throw e; });
    } : onFinally
  );
} });


/***/ }),
/* 518 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

// https://github.com/tc39/proposal-promise-try
var $export = __webpack_require__(9);
var newPromiseCapability = __webpack_require__(193);
var perform = __webpack_require__(284);

$export($export.S, 'Promise', { 'try': function (callbackfn) {
  var promiseCapability = newPromiseCapability.f(this);
  var result = perform(callbackfn);
  (result.e ? promiseCapability.reject : promiseCapability.resolve)(result.v);
  return promiseCapability.promise;
} });


/***/ }),
/* 519 */
/***/ (function(module, exports, __webpack_require__) {

module.exports = __webpack_require__(286).BrotliDecompressBuffer;


/***/ }),
/* 520 */
/***/ (function(module, exports) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.

   Bit reading helpers
*/

var BROTLI_READ_SIZE = 4096;
var BROTLI_IBUF_SIZE =  (2 * BROTLI_READ_SIZE + 32);
var BROTLI_IBUF_MASK =  (2 * BROTLI_READ_SIZE - 1);

var kBitMask = new Uint32Array([
  0, 1, 3, 7, 15, 31, 63, 127, 255, 511, 1023, 2047, 4095, 8191, 16383, 32767,
  65535, 131071, 262143, 524287, 1048575, 2097151, 4194303, 8388607, 16777215
]);

/* Input byte buffer, consist of a ringbuffer and a "slack" region where */
/* bytes from the start of the ringbuffer are copied. */
function BrotliBitReader(input) {
  this.buf_ = new Uint8Array(BROTLI_IBUF_SIZE);
  this.input_ = input;    /* input callback */
  
  this.reset();
}

BrotliBitReader.READ_SIZE = BROTLI_READ_SIZE;
BrotliBitReader.IBUF_MASK = BROTLI_IBUF_MASK;

BrotliBitReader.prototype.reset = function() {
  this.buf_ptr_ = 0;      /* next input will write here */
  this.val_ = 0;          /* pre-fetched bits */
  this.pos_ = 0;          /* byte position in stream */
  this.bit_pos_ = 0;      /* current bit-reading position in val_ */
  this.bit_end_pos_ = 0;  /* bit-reading end position from LSB of val_ */
  this.eos_ = 0;          /* input stream is finished */
  
  this.readMoreInput();
  for (var i = 0; i < 4; i++) {
    this.val_ |= this.buf_[this.pos_] << (8 * i);
    ++this.pos_;
  }
  
  return this.bit_end_pos_ > 0;
};

/* Fills up the input ringbuffer by calling the input callback.

   Does nothing if there are at least 32 bytes present after current position.

   Returns 0 if either:
    - the input callback returned an error, or
    - there is no more input and the position is past the end of the stream.

   After encountering the end of the input stream, 32 additional zero bytes are
   copied to the ringbuffer, therefore it is safe to call this function after
   every 32 bytes of input is read.
*/
BrotliBitReader.prototype.readMoreInput = function() {
  if (this.bit_end_pos_ > 256) {
    return;
  } else if (this.eos_) {
    if (this.bit_pos_ > this.bit_end_pos_)
      throw new Error('Unexpected end of input ' + this.bit_pos_ + ' ' + this.bit_end_pos_);
  } else {
    var dst = this.buf_ptr_;
    var bytes_read = this.input_.read(this.buf_, dst, BROTLI_READ_SIZE);
    if (bytes_read < 0) {
      throw new Error('Unexpected end of input');
    }
    
    if (bytes_read < BROTLI_READ_SIZE) {
      this.eos_ = 1;
      /* Store 32 bytes of zero after the stream end. */
      for (var p = 0; p < 32; p++)
        this.buf_[dst + bytes_read + p] = 0;
    }
    
    if (dst === 0) {
      /* Copy the head of the ringbuffer to the slack region. */
      for (var p = 0; p < 32; p++)
        this.buf_[(BROTLI_READ_SIZE << 1) + p] = this.buf_[p];

      this.buf_ptr_ = BROTLI_READ_SIZE;
    } else {
      this.buf_ptr_ = 0;
    }
    
    this.bit_end_pos_ += bytes_read << 3;
  }
};

/* Guarantees that there are at least 24 bits in the buffer. */
BrotliBitReader.prototype.fillBitWindow = function() {    
  while (this.bit_pos_ >= 8) {
    this.val_ >>>= 8;
    this.val_ |= this.buf_[this.pos_ & BROTLI_IBUF_MASK] << 24;
    ++this.pos_;
    this.bit_pos_ = this.bit_pos_ - 8 >>> 0;
    this.bit_end_pos_ = this.bit_end_pos_ - 8 >>> 0;
  }
};

/* Reads the specified number of bits from Read Buffer. */
BrotliBitReader.prototype.readBits = function(n_bits) {
  if (32 - this.bit_pos_ < n_bits) {
    this.fillBitWindow();
  }
  
  var val = ((this.val_ >>> this.bit_pos_) & kBitMask[n_bits]);
  this.bit_pos_ += n_bits;
  return val;
};

module.exports = BrotliBitReader;


/***/ }),
/* 521 */
/***/ (function(module, exports, __webpack_require__) {

var base64 = __webpack_require__(522);
var fs = __webpack_require__(53);

/**
 * The normal dictionary-data.js is quite large, which makes it 
 * unsuitable for browser usage. In order to make it smaller, 
 * we read dictionary.bin, which is a compressed version of
 * the dictionary, and on initial load, Brotli decompresses 
 * it's own dictionary. 😜
 */
exports.init = function() {
  var BrotliDecompressBuffer = __webpack_require__(286).BrotliDecompressBuffer;
  var compressed = base64.toByteArray(__webpack_require__(523));
  return BrotliDecompressBuffer(compressed);
};


/***/ }),
/* 522 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


exports.byteLength = byteLength
exports.toByteArray = toByteArray
exports.fromByteArray = fromByteArray

var lookup = []
var revLookup = []
var Arr = typeof Uint8Array !== 'undefined' ? Uint8Array : Array

var code = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
for (var i = 0, len = code.length; i < len; ++i) {
  lookup[i] = code[i]
  revLookup[code.charCodeAt(i)] = i
}

// Support decoding URL-safe base64 strings, as Node.js does.
// See: https://en.wikipedia.org/wiki/Base64#URL_applications
revLookup['-'.charCodeAt(0)] = 62
revLookup['_'.charCodeAt(0)] = 63

function getLens (b64) {
  var len = b64.length

  if (len % 4 > 0) {
    throw new Error('Invalid string. Length must be a multiple of 4')
  }

  // Trim off extra bytes after placeholder bytes are found
  // See: https://github.com/beatgammit/base64-js/issues/42
  var validLen = b64.indexOf('=')
  if (validLen === -1) validLen = len

  var placeHoldersLen = validLen === len
    ? 0
    : 4 - (validLen % 4)

  return [validLen, placeHoldersLen]
}

// base64 is 4/3 + up to two characters of the original data
function byteLength (b64) {
  var lens = getLens(b64)
  var validLen = lens[0]
  var placeHoldersLen = lens[1]
  return ((validLen + placeHoldersLen) * 3 / 4) - placeHoldersLen
}

function _byteLength (b64, validLen, placeHoldersLen) {
  return ((validLen + placeHoldersLen) * 3 / 4) - placeHoldersLen
}

function toByteArray (b64) {
  var tmp
  var lens = getLens(b64)
  var validLen = lens[0]
  var placeHoldersLen = lens[1]

  var arr = new Arr(_byteLength(b64, validLen, placeHoldersLen))

  var curByte = 0

  // if there are placeholders, only get up to the last complete 4 chars
  var len = placeHoldersLen > 0
    ? validLen - 4
    : validLen

  var i
  for (i = 0; i < len; i += 4) {
    tmp =
      (revLookup[b64.charCodeAt(i)] << 18) |
      (revLookup[b64.charCodeAt(i + 1)] << 12) |
      (revLookup[b64.charCodeAt(i + 2)] << 6) |
      revLookup[b64.charCodeAt(i + 3)]
    arr[curByte++] = (tmp >> 16) & 0xFF
    arr[curByte++] = (tmp >> 8) & 0xFF
    arr[curByte++] = tmp & 0xFF
  }

  if (placeHoldersLen === 2) {
    tmp =
      (revLookup[b64.charCodeAt(i)] << 2) |
      (revLookup[b64.charCodeAt(i + 1)] >> 4)
    arr[curByte++] = tmp & 0xFF
  }

  if (placeHoldersLen === 1) {
    tmp =
      (revLookup[b64.charCodeAt(i)] << 10) |
      (revLookup[b64.charCodeAt(i + 1)] << 4) |
      (revLookup[b64.charCodeAt(i + 2)] >> 2)
    arr[curByte++] = (tmp >> 8) & 0xFF
    arr[curByte++] = tmp & 0xFF
  }

  return arr
}

function tripletToBase64 (num) {
  return lookup[num >> 18 & 0x3F] +
    lookup[num >> 12 & 0x3F] +
    lookup[num >> 6 & 0x3F] +
    lookup[num & 0x3F]
}

function encodeChunk (uint8, start, end) {
  var tmp
  var output = []
  for (var i = start; i < end; i += 3) {
    tmp =
      ((uint8[i] << 16) & 0xFF0000) +
      ((uint8[i + 1] << 8) & 0xFF00) +
      (uint8[i + 2] & 0xFF)
    output.push(tripletToBase64(tmp))
  }
  return output.join('')
}

function fromByteArray (uint8) {
  var tmp
  var len = uint8.length
  var extraBytes = len % 3 // if we have 1 byte left, pad 2 bytes
  var parts = []
  var maxChunkLength = 16383 // must be multiple of 3

  // go through the array every three bytes, we'll deal with trailing stuff later
  for (var i = 0, len2 = len - extraBytes; i < len2; i += maxChunkLength) {
    parts.push(encodeChunk(
      uint8, i, (i + maxChunkLength) > len2 ? len2 : (i + maxChunkLength)
    ))
  }

  // pad the end with zeros, but make sure to not forget the extra bytes
  if (extraBytes === 1) {
    tmp = uint8[len - 1]
    parts.push(
      lookup[tmp >> 2] +
      lookup[(tmp << 4) & 0x3F] +
      '=='
    )
  } else if (extraBytes === 2) {
    tmp = (uint8[len - 2] << 8) + uint8[len - 1]
    parts.push(
      lookup[tmp >> 10] +
      lookup[(tmp >> 4) & 0x3F] +
      lookup[(tmp << 2) & 0x3F] +
      '='
    )
  }

  return parts.join('')
}


/***/ }),
/* 523 */
/***/ (function(module, exports) {

module.exports="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";


/***/ }),
/* 524 */
/***/ (function(module, exports) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.

   Lookup table to map the previous two bytes to a context id.

   There are four different context modeling modes defined here:
     CONTEXT_LSB6: context id is the least significant 6 bits of the last byte,
     CONTEXT_MSB6: context id is the most significant 6 bits of the last byte,
     CONTEXT_UTF8: second-order context model tuned for UTF8-encoded text,
     CONTEXT_SIGNED: second-order context model tuned for signed integers.

   The context id for the UTF8 context model is calculated as follows. If p1
   and p2 are the previous two bytes, we calcualte the context as

     context = kContextLookup[p1] | kContextLookup[p2 + 256].

   If the previous two bytes are ASCII characters (i.e. < 128), this will be
   equivalent to

     context = 4 * context1(p1) + context2(p2),

   where context1 is based on the previous byte in the following way:

     0  : non-ASCII control
     1  : \t, \n, \r
     2  : space
     3  : other punctuation
     4  : " '
     5  : %
     6  : ( < [ {
     7  : ) > ] }
     8  : , ; :
     9  : .
     10 : =
     11 : number
     12 : upper-case vowel
     13 : upper-case consonant
     14 : lower-case vowel
     15 : lower-case consonant

   and context2 is based on the second last byte:

     0 : control, space
     1 : punctuation
     2 : upper-case letter, number
     3 : lower-case letter

   If the last byte is ASCII, and the second last byte is not (in a valid UTF8
   stream it will be a continuation byte, value between 128 and 191), the
   context is the same as if the second last byte was an ASCII control or space.

   If the last byte is a UTF8 lead byte (value >= 192), then the next byte will
   be a continuation byte and the context id is 2 or 3 depending on the LSB of
   the last byte and to a lesser extent on the second last byte if it is ASCII.

   If the last byte is a UTF8 continuation byte, the second last byte can be:
     - continuation byte: the next byte is probably ASCII or lead byte (assuming
       4-byte UTF8 characters are rare) and the context id is 0 or 1.
     - lead byte (192 - 207): next byte is ASCII or lead byte, context is 0 or 1
     - lead byte (208 - 255): next byte is continuation byte, context is 2 or 3

   The possible value combinations of the previous two bytes, the range of
   context ids and the type of the next byte is summarized in the table below:

   |--------\-----------------------------------------------------------------|
   |         \                         Last byte                              |
   | Second   \---------------------------------------------------------------|
   | last byte \    ASCII            |   cont. byte        |   lead byte      |
   |            \   (0-127)          |   (128-191)         |   (192-)         |
   |=============|===================|=====================|==================|
   |  ASCII      | next: ASCII/lead  |  not valid          |  next: cont.     |
   |  (0-127)    | context: 4 - 63   |                     |  context: 2 - 3  |
   |-------------|-------------------|---------------------|------------------|
   |  cont. byte | next: ASCII/lead  |  next: ASCII/lead   |  next: cont.     |
   |  (128-191)  | context: 4 - 63   |  context: 0 - 1     |  context: 2 - 3  |
   |-------------|-------------------|---------------------|------------------|
   |  lead byte  | not valid         |  next: ASCII/lead   |  not valid       |
   |  (192-207)  |                   |  context: 0 - 1     |                  |
   |-------------|-------------------|---------------------|------------------|
   |  lead byte  | not valid         |  next: cont.        |  not valid       |
   |  (208-)     |                   |  context: 2 - 3     |                  |
   |-------------|-------------------|---------------------|------------------|

   The context id for the signed context mode is calculated as:

     context = (kContextLookup[512 + p1] << 3) | kContextLookup[512 + p2].

   For any context modeling modes, the context ids can be calculated by |-ing
   together two lookups from one table using context model dependent offsets:

     context = kContextLookup[offset1 + p1] | kContextLookup[offset2 + p2].

   where offset1 and offset2 are dependent on the context mode.
*/

var CONTEXT_LSB6         = 0;
var CONTEXT_MSB6         = 1;
var CONTEXT_UTF8         = 2;
var CONTEXT_SIGNED       = 3;

/* Common context lookup table for all context modes. */
exports.lookup = new Uint8Array([
  /* CONTEXT_UTF8, last byte. */
  /* ASCII range. */
   0,  0,  0,  0,  0,  0,  0,  0,  0,  4,  4,  0,  0,  4,  0,  0,
   0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,
   8, 12, 16, 12, 12, 20, 12, 16, 24, 28, 12, 12, 32, 12, 36, 12,
  44, 44, 44, 44, 44, 44, 44, 44, 44, 44, 32, 32, 24, 40, 28, 12,
  12, 48, 52, 52, 52, 48, 52, 52, 52, 48, 52, 52, 52, 52, 52, 48,
  52, 52, 52, 52, 52, 48, 52, 52, 52, 52, 52, 24, 12, 28, 12, 12,
  12, 56, 60, 60, 60, 56, 60, 60, 60, 56, 60, 60, 60, 60, 60, 56,
  60, 60, 60, 60, 60, 56, 60, 60, 60, 60, 60, 24, 12, 28, 12,  0,
  /* UTF8 continuation byte range. */
  0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1,
  0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1,
  0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1,
  0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1,
  /* UTF8 lead byte range. */
  2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3,
  2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3,
  2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3,
  2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3,
  /* CONTEXT_UTF8 second last byte. */
  /* ASCII range. */
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 1, 1, 1, 1,
  1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 1, 1, 1,
  1, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
  3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 1, 1, 1, 1, 0,
  /* UTF8 continuation byte range. */
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  /* UTF8 lead byte range. */
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  /* CONTEXT_SIGNED, second last byte. */
  0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
  3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
  3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
  3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5,
  5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5,
  5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5,
  6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 7,
  /* CONTEXT_SIGNED, last byte, same as the above values shifted by 3 bits. */
   0, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8,
  16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16,
  16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16,
  16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16,
  24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32,
  32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32,
  32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32,
  32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32,
  40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40,
  40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40,
  40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40,
  48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 56,
  /* CONTEXT_LSB6, last byte. */
   0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
  32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
  48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63,
   0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
  32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
  48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63,
   0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
  32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
  48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63,
   0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
  32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
  48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63,
  /* CONTEXT_MSB6, last byte. */
   0,  0,  0,  0,  1,  1,  1,  1,  2,  2,  2,  2,  3,  3,  3,  3,
   4,  4,  4,  4,  5,  5,  5,  5,  6,  6,  6,  6,  7,  7,  7,  7,
   8,  8,  8,  8,  9,  9,  9,  9, 10, 10, 10, 10, 11, 11, 11, 11,
  12, 12, 12, 12, 13, 13, 13, 13, 14, 14, 14, 14, 15, 15, 15, 15,
  16, 16, 16, 16, 17, 17, 17, 17, 18, 18, 18, 18, 19, 19, 19, 19,
  20, 20, 20, 20, 21, 21, 21, 21, 22, 22, 22, 22, 23, 23, 23, 23,
  24, 24, 24, 24, 25, 25, 25, 25, 26, 26, 26, 26, 27, 27, 27, 27,
  28, 28, 28, 28, 29, 29, 29, 29, 30, 30, 30, 30, 31, 31, 31, 31,
  32, 32, 32, 32, 33, 33, 33, 33, 34, 34, 34, 34, 35, 35, 35, 35,
  36, 36, 36, 36, 37, 37, 37, 37, 38, 38, 38, 38, 39, 39, 39, 39,
  40, 40, 40, 40, 41, 41, 41, 41, 42, 42, 42, 42, 43, 43, 43, 43,
  44, 44, 44, 44, 45, 45, 45, 45, 46, 46, 46, 46, 47, 47, 47, 47,
  48, 48, 48, 48, 49, 49, 49, 49, 50, 50, 50, 50, 51, 51, 51, 51,
  52, 52, 52, 52, 53, 53, 53, 53, 54, 54, 54, 54, 55, 55, 55, 55,
  56, 56, 56, 56, 57, 57, 57, 57, 58, 58, 58, 58, 59, 59, 59, 59,
  60, 60, 60, 60, 61, 61, 61, 61, 62, 62, 62, 62, 63, 63, 63, 63,
  /* CONTEXT_{M,L}SB6, second last byte, */
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
]);

exports.lookupOffsets = new Uint16Array([
  /* CONTEXT_LSB6 */
  1024, 1536,
  /* CONTEXT_MSB6 */
  1280, 1536,
  /* CONTEXT_UTF8 */
  0, 256,
  /* CONTEXT_SIGNED */
  768, 512,
]);


/***/ }),
/* 525 */
/***/ (function(module, exports) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.

   Lookup tables to map prefix codes to value ranges. This is used during
   decoding of the block lengths, literal insertion lengths and copy lengths.
*/

/* Represents the range of values belonging to a prefix code: */
/* [offset, offset + 2^nbits) */
function PrefixCodeRange(offset, nbits) {
  this.offset = offset;
  this.nbits = nbits;
}

exports.kBlockLengthPrefixCode = [
  new PrefixCodeRange(1, 2), new PrefixCodeRange(5, 2), new PrefixCodeRange(9, 2), new PrefixCodeRange(13, 2),
  new PrefixCodeRange(17, 3), new PrefixCodeRange(25, 3), new PrefixCodeRange(33, 3), new PrefixCodeRange(41, 3),
  new PrefixCodeRange(49, 4), new PrefixCodeRange(65, 4), new PrefixCodeRange(81, 4), new PrefixCodeRange(97, 4),
  new PrefixCodeRange(113, 5), new PrefixCodeRange(145, 5), new PrefixCodeRange(177, 5), new PrefixCodeRange(209, 5),
  new PrefixCodeRange(241, 6), new PrefixCodeRange(305, 6), new PrefixCodeRange(369, 7), new PrefixCodeRange(497, 8),
  new PrefixCodeRange(753, 9), new PrefixCodeRange(1265, 10), new PrefixCodeRange(2289, 11), new PrefixCodeRange(4337, 12),
  new PrefixCodeRange(8433, 13), new PrefixCodeRange(16625, 24)
];

exports.kInsertLengthPrefixCode = [
  new PrefixCodeRange(0, 0), new PrefixCodeRange(1, 0), new PrefixCodeRange(2, 0), new PrefixCodeRange(3, 0),
  new PrefixCodeRange(4, 0), new PrefixCodeRange(5, 0), new PrefixCodeRange(6, 1), new PrefixCodeRange(8, 1),
  new PrefixCodeRange(10, 2), new PrefixCodeRange(14, 2), new PrefixCodeRange(18, 3), new PrefixCodeRange(26, 3),
  new PrefixCodeRange(34, 4), new PrefixCodeRange(50, 4), new PrefixCodeRange(66, 5), new PrefixCodeRange(98, 5),
  new PrefixCodeRange(130, 6), new PrefixCodeRange(194, 7), new PrefixCodeRange(322, 8), new PrefixCodeRange(578, 9),
  new PrefixCodeRange(1090, 10), new PrefixCodeRange(2114, 12), new PrefixCodeRange(6210, 14), new PrefixCodeRange(22594, 24),
];

exports.kCopyLengthPrefixCode = [
  new PrefixCodeRange(2, 0), new PrefixCodeRange(3, 0), new PrefixCodeRange(4, 0), new PrefixCodeRange(5, 0),
  new PrefixCodeRange(6, 0), new PrefixCodeRange(7, 0), new PrefixCodeRange(8, 0), new PrefixCodeRange(9, 0),
  new PrefixCodeRange(10, 1), new PrefixCodeRange(12, 1), new PrefixCodeRange(14, 2), new PrefixCodeRange(18, 2),
  new PrefixCodeRange(22, 3), new PrefixCodeRange(30, 3), new PrefixCodeRange(38, 4), new PrefixCodeRange(54, 4),
  new PrefixCodeRange(70, 5), new PrefixCodeRange(102, 5), new PrefixCodeRange(134, 6), new PrefixCodeRange(198, 7),
  new PrefixCodeRange(326, 8), new PrefixCodeRange(582, 9), new PrefixCodeRange(1094, 10), new PrefixCodeRange(2118, 24),
];

exports.kInsertRangeLut = [
  0, 0, 8, 8, 0, 16, 8, 16, 16,
];

exports.kCopyRangeLut = [
  0, 8, 0, 8, 16, 0, 16, 8, 16,
];


/***/ }),
/* 526 */
/***/ (function(module, exports, __webpack_require__) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.

   Transformations on dictionary words.
*/

var BrotliDictionary = __webpack_require__(288);

var kIdentity       = 0;
var kOmitLast1      = 1;
var kOmitLast2      = 2;
var kOmitLast3      = 3;
var kOmitLast4      = 4;
var kOmitLast5      = 5;
var kOmitLast6      = 6;
var kOmitLast7      = 7;
var kOmitLast8      = 8;
var kOmitLast9      = 9;
var kUppercaseFirst = 10;
var kUppercaseAll   = 11;
var kOmitFirst1     = 12;
var kOmitFirst2     = 13;
var kOmitFirst3     = 14;
var kOmitFirst4     = 15;
var kOmitFirst5     = 16;
var kOmitFirst6     = 17;
var kOmitFirst7     = 18;
var kOmitFirst8     = 19;
var kOmitFirst9     = 20;

function Transform(prefix, transform, suffix) {
  this.prefix = new Uint8Array(prefix.length);
  this.transform = transform;
  this.suffix = new Uint8Array(suffix.length);
  
  for (var i = 0; i < prefix.length; i++)
    this.prefix[i] = prefix.charCodeAt(i);
  
  for (var i = 0; i < suffix.length; i++)
    this.suffix[i] = suffix.charCodeAt(i);
}

var kTransforms = [
     new Transform(         "", kIdentity,       ""           ),
     new Transform(         "", kIdentity,       " "          ),
     new Transform(        " ", kIdentity,       " "          ),
     new Transform(         "", kOmitFirst1,     ""           ),
     new Transform(         "", kUppercaseFirst, " "          ),
     new Transform(         "", kIdentity,       " the "      ),
     new Transform(        " ", kIdentity,       ""           ),
     new Transform(       "s ", kIdentity,       " "          ),
     new Transform(         "", kIdentity,       " of "       ),
     new Transform(         "", kUppercaseFirst, ""           ),
     new Transform(         "", kIdentity,       " and "      ),
     new Transform(         "", kOmitFirst2,     ""           ),
     new Transform(         "", kOmitLast1,      ""           ),
     new Transform(       ", ", kIdentity,       " "          ),
     new Transform(         "", kIdentity,       ", "         ),
     new Transform(        " ", kUppercaseFirst, " "          ),
     new Transform(         "", kIdentity,       " in "       ),
     new Transform(         "", kIdentity,       " to "       ),
     new Transform(       "e ", kIdentity,       " "          ),
     new Transform(         "", kIdentity,       "\""         ),
     new Transform(         "", kIdentity,       "."          ),
     new Transform(         "", kIdentity,       "\">"        ),
     new Transform(         "", kIdentity,       "\n"         ),
     new Transform(         "", kOmitLast3,      ""           ),
     new Transform(         "", kIdentity,       "]"          ),
     new Transform(         "", kIdentity,       " for "      ),
     new Transform(         "", kOmitFirst3,     ""           ),
     new Transform(         "", kOmitLast2,      ""           ),
     new Transform(         "", kIdentity,       " a "        ),
     new Transform(         "", kIdentity,       " that "     ),
     new Transform(        " ", kUppercaseFirst, ""           ),
     new Transform(         "", kIdentity,       ". "         ),
     new Transform(        ".", kIdentity,       ""           ),
     new Transform(        " ", kIdentity,       ", "         ),
     new Transform(         "", kOmitFirst4,     ""           ),
     new Transform(         "", kIdentity,       " with "     ),
     new Transform(         "", kIdentity,       "'"          ),
     new Transform(         "", kIdentity,       " from "     ),
     new Transform(         "", kIdentity,       " by "       ),
     new Transform(         "", kOmitFirst5,     ""           ),
     new Transform(         "", kOmitFirst6,     ""           ),
     new Transform(    " the ", kIdentity,       ""           ),
     new Transform(         "", kOmitLast4,      ""           ),
     new Transform(         "", kIdentity,       ". The "     ),
     new Transform(         "", kUppercaseAll,   ""           ),
     new Transform(         "", kIdentity,       " on "       ),
     new Transform(         "", kIdentity,       " as "       ),
     new Transform(         "", kIdentity,       " is "       ),
     new Transform(         "", kOmitLast7,      ""           ),
     new Transform(         "", kOmitLast1,      "ing "       ),
     new Transform(         "", kIdentity,       "\n\t"       ),
     new Transform(         "", kIdentity,       ":"          ),
     new Transform(        " ", kIdentity,       ". "         ),
     new Transform(         "", kIdentity,       "ed "        ),
     new Transform(         "", kOmitFirst9,     ""           ),
     new Transform(         "", kOmitFirst7,     ""           ),
     new Transform(         "", kOmitLast6,      ""           ),
     new Transform(         "", kIdentity,       "("          ),
     new Transform(         "", kUppercaseFirst, ", "         ),
     new Transform(         "", kOmitLast8,      ""           ),
     new Transform(         "", kIdentity,       " at "       ),
     new Transform(         "", kIdentity,       "ly "        ),
     new Transform(    " the ", kIdentity,       " of "       ),
     new Transform(         "", kOmitLast5,      ""           ),
     new Transform(         "", kOmitLast9,      ""           ),
     new Transform(        " ", kUppercaseFirst, ", "         ),
     new Transform(         "", kUppercaseFirst, "\""         ),
     new Transform(        ".", kIdentity,       "("          ),
     new Transform(         "", kUppercaseAll,   " "          ),
     new Transform(         "", kUppercaseFirst, "\">"        ),
     new Transform(         "", kIdentity,       "=\""        ),
     new Transform(        " ", kIdentity,       "."          ),
     new Transform(    ".com/", kIdentity,       ""           ),
     new Transform(    " the ", kIdentity,       " of the "   ),
     new Transform(         "", kUppercaseFirst, "'"          ),
     new Transform(         "", kIdentity,       ". This "    ),
     new Transform(         "", kIdentity,       ","          ),
     new Transform(        ".", kIdentity,       " "          ),
     new Transform(         "", kUppercaseFirst, "("          ),
     new Transform(         "", kUppercaseFirst, "."          ),
     new Transform(         "", kIdentity,       " not "      ),
     new Transform(        " ", kIdentity,       "=\""        ),
     new Transform(         "", kIdentity,       "er "        ),
     new Transform(        " ", kUppercaseAll,   " "          ),
     new Transform(         "", kIdentity,       "al "        ),
     new Transform(        " ", kUppercaseAll,   ""           ),
     new Transform(         "", kIdentity,       "='"         ),
     new Transform(         "", kUppercaseAll,   "\""         ),
     new Transform(         "", kUppercaseFirst, ". "         ),
     new Transform(        " ", kIdentity,       "("          ),
     new Transform(         "", kIdentity,       "ful "       ),
     new Transform(        " ", kUppercaseFirst, ". "         ),
     new Transform(         "", kIdentity,       "ive "       ),
     new Transform(         "", kIdentity,       "less "      ),
     new Transform(         "", kUppercaseAll,   "'"          ),
     new Transform(         "", kIdentity,       "est "       ),
     new Transform(        " ", kUppercaseFirst, "."          ),
     new Transform(         "", kUppercaseAll,   "\">"        ),
     new Transform(        " ", kIdentity,       "='"         ),
     new Transform(         "", kUppercaseFirst, ","          ),
     new Transform(         "", kIdentity,       "ize "       ),
     new Transform(         "", kUppercaseAll,   "."          ),
     new Transform( "\xc2\xa0", kIdentity,       ""           ),
     new Transform(        " ", kIdentity,       ","          ),
     new Transform(         "", kUppercaseFirst, "=\""        ),
     new Transform(         "", kUppercaseAll,   "=\""        ),
     new Transform(         "", kIdentity,       "ous "       ),
     new Transform(         "", kUppercaseAll,   ", "         ),
     new Transform(         "", kUppercaseFirst, "='"         ),
     new Transform(        " ", kUppercaseFirst, ","          ),
     new Transform(        " ", kUppercaseAll,   "=\""        ),
     new Transform(        " ", kUppercaseAll,   ", "         ),
     new Transform(         "", kUppercaseAll,   ","          ),
     new Transform(         "", kUppercaseAll,   "("          ),
     new Transform(         "", kUppercaseAll,   ". "         ),
     new Transform(        " ", kUppercaseAll,   "."          ),
     new Transform(         "", kUppercaseAll,   "='"         ),
     new Transform(        " ", kUppercaseAll,   ". "         ),
     new Transform(        " ", kUppercaseFirst, "=\""        ),
     new Transform(        " ", kUppercaseAll,   "='"         ),
     new Transform(        " ", kUppercaseFirst, "='"         )
];

exports.kTransforms = kTransforms;
exports.kNumTransforms = kTransforms.length;

function ToUpperCase(p, i) {
  if (p[i] < 0xc0) {
    if (p[i] >= 97 && p[i] <= 122) {
      p[i] ^= 32;
    }
    return 1;
  }
  
  /* An overly simplified uppercasing model for utf-8. */
  if (p[i] < 0xe0) {
    p[i + 1] ^= 32;
    return 2;
  }
  
  /* An arbitrary transform for three byte characters. */
  p[i + 2] ^= 5;
  return 3;
}

exports.transformDictionaryWord = function(dst, idx, word, len, transform) {
  var prefix = kTransforms[transform].prefix;
  var suffix = kTransforms[transform].suffix;
  var t = kTransforms[transform].transform;
  var skip = t < kOmitFirst1 ? 0 : t - (kOmitFirst1 - 1);
  var i = 0;
  var start_idx = idx;
  var uppercase;
  
  if (skip > len) {
    skip = len;
  }
  
  var prefix_pos = 0;
  while (prefix_pos < prefix.length) {
    dst[idx++] = prefix[prefix_pos++];
  }
  
  word += skip;
  len -= skip;
  
  if (t <= kOmitLast9) {
    len -= t;
  }
  
  for (i = 0; i < len; i++) {
    dst[idx++] = BrotliDictionary.dictionary[word + i];
  }
  
  uppercase = idx - len;
  
  if (t === kUppercaseFirst) {
    ToUpperCase(dst, uppercase);
  } else if (t === kUppercaseAll) {
    while (len > 0) {
      var step = ToUpperCase(dst, uppercase);
      uppercase += step;
      len -= step;
    }
  }
  
  var suffix_pos = 0;
  while (suffix_pos < suffix.length) {
    dst[idx++] = suffix[suffix_pos++];
  }
  
  return idx - start_idx;
}


/***/ }),
/* 527 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(28);

__webpack_require__(46);

__webpack_require__(61);

__webpack_require__(32);

__webpack_require__(62);

__webpack_require__(134);

__webpack_require__(63);

__webpack_require__(64);

__webpack_require__(65);

__webpack_require__(66);

__webpack_require__(67);

__webpack_require__(68);

__webpack_require__(69);

__webpack_require__(70);

__webpack_require__(71);

__webpack_require__(72);

__webpack_require__(73);

__webpack_require__(74);

__webpack_require__(75);

__webpack_require__(76);

__webpack_require__(77);

__webpack_require__(78);

__webpack_require__(79);

__webpack_require__(80);

__webpack_require__(81);

__webpack_require__(82);

__webpack_require__(83);

__webpack_require__(84);

__webpack_require__(85);

var inflate = __webpack_require__(135); // Shift size for getting the index-1 table offset.


var SHIFT_1 = 6 + 5; // Shift size for getting the index-2 table offset.

var SHIFT_2 = 5; // Difference between the two shift sizes,
// for getting an index-1 offset from an index-2 offset. 6=11-5

var SHIFT_1_2 = SHIFT_1 - SHIFT_2; // Number of index-1 entries for the BMP. 32=0x20
// This part of the index-1 table is omitted from the serialized form.

var OMITTED_BMP_INDEX_1_LENGTH = 0x10000 >> SHIFT_1; // Number of entries in an index-2 block. 64=0x40

var INDEX_2_BLOCK_LENGTH = 1 << SHIFT_1_2; // Mask for getting the lower bits for the in-index-2-block offset. */

var INDEX_2_MASK = INDEX_2_BLOCK_LENGTH - 1; // Shift size for shifting left the index array values.
// Increases possible data size with 16-bit index values at the cost
// of compactability.
// This requires data blocks to be aligned by DATA_GRANULARITY.

var INDEX_SHIFT = 2; // Number of entries in a data block. 32=0x20

var DATA_BLOCK_LENGTH = 1 << SHIFT_2; // Mask for getting the lower bits for the in-data-block offset.

var DATA_MASK = DATA_BLOCK_LENGTH - 1; // The part of the index-2 table for U+D800..U+DBFF stores values for
// lead surrogate code _units_ not code _points_.
// Values for lead surrogate code _points_ are indexed with this portion of the table.
// Length=32=0x20=0x400>>SHIFT_2. (There are 1024=0x400 lead surrogates.)

var LSCP_INDEX_2_OFFSET = 0x10000 >> SHIFT_2;
var LSCP_INDEX_2_LENGTH = 0x400 >> SHIFT_2; // Count the lengths of both BMP pieces. 2080=0x820

var INDEX_2_BMP_LENGTH = LSCP_INDEX_2_OFFSET + LSCP_INDEX_2_LENGTH; // The 2-byte UTF-8 version of the index-2 table follows at offset 2080=0x820.
// Length 32=0x20 for lead bytes C0..DF, regardless of SHIFT_2.

var UTF8_2B_INDEX_2_OFFSET = INDEX_2_BMP_LENGTH;
var UTF8_2B_INDEX_2_LENGTH = 0x800 >> 6; // U+0800 is the first code point after 2-byte UTF-8
// The index-1 table, only used for supplementary code points, at offset 2112=0x840.
// Variable length, for code points up to highStart, where the last single-value range starts.
// Maximum length 512=0x200=0x100000>>SHIFT_1.
// (For 0x100000 supplementary code points U+10000..U+10ffff.)
//
// The part of the index-2 table for supplementary code points starts
// after this index-1 table.
//
// Both the index-1 table and the following part of the index-2 table
// are omitted completely if there is only BMP data.

var INDEX_1_OFFSET = UTF8_2B_INDEX_2_OFFSET + UTF8_2B_INDEX_2_LENGTH; // The alignment size of a data block. Also the granularity for compaction.

var DATA_GRANULARITY = 1 << INDEX_SHIFT;

var UnicodeTrie = /*#__PURE__*/function () {
  function UnicodeTrie(data) {
    var isBuffer = typeof data.readUInt32BE === 'function' && typeof data.slice === 'function';

    if (isBuffer || data instanceof Uint8Array) {
      // read binary format
      var uncompressedLength;

      if (isBuffer) {
        this.highStart = data.readUInt32BE(0);
        this.errorValue = data.readUInt32BE(4);
        uncompressedLength = data.readUInt32BE(8);
        data = data.slice(12);
      } else {
        var view = new DataView(data.buffer);
        this.highStart = view.getUint32(0);
        this.errorValue = view.getUint32(4);
        uncompressedLength = view.getUint32(8);
        data = data.subarray(12);
      } // double inflate the actual trie data


      data = inflate(data, new Uint8Array(uncompressedLength));
      data = inflate(data, new Uint8Array(uncompressedLength));
      this.data = new Uint32Array(data.buffer);
    } else {
      // pre-parsed data
      var _data = data;
      this.data = _data.data;
      this.highStart = _data.highStart;
      this.errorValue = _data.errorValue;
    }
  }

  var _proto = UnicodeTrie.prototype;

  _proto.get = function get(codePoint) {
    var index;

    if (codePoint < 0 || codePoint > 0x10ffff) {
      return this.errorValue;
    }

    if (codePoint < 0xd800 || codePoint > 0xdbff && codePoint <= 0xffff) {
      // Ordinary BMP code point, excluding leading surrogates.
      // BMP uses a single level lookup.  BMP index starts at offset 0 in the index.
      // data is stored in the index array itself.
      index = (this.data[codePoint >> SHIFT_2] << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }

    if (codePoint <= 0xffff) {
      // Lead Surrogate Code Point.  A Separate index section is stored for
      // lead surrogate code units and code points.
      //   The main index has the code unit data.
      //   For this function, we need the code point data.
      index = (this.data[LSCP_INDEX_2_OFFSET + (codePoint - 0xd800 >> SHIFT_2)] << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }

    if (codePoint < this.highStart) {
      // Supplemental code point, use two-level lookup.
      index = this.data[INDEX_1_OFFSET - OMITTED_BMP_INDEX_1_LENGTH + (codePoint >> SHIFT_1)];
      index = this.data[index + (codePoint >> SHIFT_2 & INDEX_2_MASK)];
      index = (index << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }

    return this.data[this.data.length - DATA_GRANULARITY];
  };

  return UnicodeTrie;
}();

module.exports = UnicodeTrie;

/***/ }),
/* 528 */
/***/ (function(module, exports, __webpack_require__) {

var lookup = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

;(function (exports) {
	'use strict';

  var Arr = (typeof Uint8Array !== 'undefined')
    ? Uint8Array
    : Array

	var PLUS   = '+'.charCodeAt(0)
	var SLASH  = '/'.charCodeAt(0)
	var NUMBER = '0'.charCodeAt(0)
	var LOWER  = 'a'.charCodeAt(0)
	var UPPER  = 'A'.charCodeAt(0)
	var PLUS_URL_SAFE = '-'.charCodeAt(0)
	var SLASH_URL_SAFE = '_'.charCodeAt(0)

	function decode (elt) {
		var code = elt.charCodeAt(0)
		if (code === PLUS ||
		    code === PLUS_URL_SAFE)
			return 62 // '+'
		if (code === SLASH ||
		    code === SLASH_URL_SAFE)
			return 63 // '/'
		if (code < NUMBER)
			return -1 //no match
		if (code < NUMBER + 10)
			return code - NUMBER + 26 + 26
		if (code < UPPER + 26)
			return code - UPPER
		if (code < LOWER + 26)
			return code - LOWER + 26
	}

	function b64ToByteArray (b64) {
		var i, j, l, tmp, placeHolders, arr

		if (b64.length % 4 > 0) {
			throw new Error('Invalid string. Length must be a multiple of 4')
		}

		// the number of equal signs (place holders)
		// if there are two placeholders, than the two characters before it
		// represent one byte
		// if there is only one, then the three characters before it represent 2 bytes
		// this is just a cheap hack to not do indexOf twice
		var len = b64.length
		placeHolders = '=' === b64.charAt(len - 2) ? 2 : '=' === b64.charAt(len - 1) ? 1 : 0

		// base64 is 4/3 + up to two characters of the original data
		arr = new Arr(b64.length * 3 / 4 - placeHolders)

		// if there are placeholders, only get up to the last complete 4 chars
		l = placeHolders > 0 ? b64.length - 4 : b64.length

		var L = 0

		function push (v) {
			arr[L++] = v
		}

		for (i = 0, j = 0; i < l; i += 4, j += 3) {
			tmp = (decode(b64.charAt(i)) << 18) | (decode(b64.charAt(i + 1)) << 12) | (decode(b64.charAt(i + 2)) << 6) | decode(b64.charAt(i + 3))
			push((tmp & 0xFF0000) >> 16)
			push((tmp & 0xFF00) >> 8)
			push(tmp & 0xFF)
		}

		if (placeHolders === 2) {
			tmp = (decode(b64.charAt(i)) << 2) | (decode(b64.charAt(i + 1)) >> 4)
			push(tmp & 0xFF)
		} else if (placeHolders === 1) {
			tmp = (decode(b64.charAt(i)) << 10) | (decode(b64.charAt(i + 1)) << 4) | (decode(b64.charAt(i + 2)) >> 2)
			push((tmp >> 8) & 0xFF)
			push(tmp & 0xFF)
		}

		return arr
	}

	function uint8ToBase64 (uint8) {
		var i,
			extraBytes = uint8.length % 3, // if we have 1 byte left, pad 2 bytes
			output = "",
			temp, length

		function encode (num) {
			return lookup.charAt(num)
		}

		function tripletToBase64 (num) {
			return encode(num >> 18 & 0x3F) + encode(num >> 12 & 0x3F) + encode(num >> 6 & 0x3F) + encode(num & 0x3F)
		}

		// go through the array every three bytes, we'll deal with trailing stuff later
		for (i = 0, length = uint8.length - extraBytes; i < length; i += 3) {
			temp = (uint8[i] << 16) + (uint8[i + 1] << 8) + (uint8[i + 2])
			output += tripletToBase64(temp)
		}

		// pad the end with zeros, but make sure to not forget the extra bytes
		switch (extraBytes) {
			case 1:
				temp = uint8[uint8.length - 1]
				output += encode(temp >> 2)
				output += encode((temp << 4) & 0x3F)
				output += '=='
				break
			case 2:
				temp = (uint8[uint8.length - 2] << 8) + (uint8[uint8.length - 1])
				output += encode(temp >> 10)
				output += encode((temp >> 4) & 0x3F)
				output += encode((temp << 2) & 0x3F)
				output += '='
				break
		}

		return output
	}

	exports.toByteArray = b64ToByteArray
	exports.fromByteArray = uint8ToBase64
}( false ? (undefined) : exports))


/***/ }),
/* 529 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// The following break classes are handled by the pair table
exports.OP = 0; // Opening punctuation

exports.CL = 1; // Closing punctuation

exports.CP = 2; // Closing parenthesis

exports.QU = 3; // Ambiguous quotation

exports.GL = 4; // Glue

exports.NS = 5; // Non-starters

exports.EX = 6; // Exclamation/Interrogation

exports.SY = 7; // Symbols allowing break after

exports.IS = 8; // Infix separator

exports.PR = 9; // Prefix

exports.PO = 10; // Postfix

exports.NU = 11; // Numeric

exports.AL = 12; // Alphabetic

exports.HL = 13; // Hebrew Letter

exports.ID = 14; // Ideographic

exports.IN = 15; // Inseparable characters

exports.HY = 16; // Hyphen

exports.BA = 17; // Break after

exports.BB = 18; // Break before

exports.B2 = 19; // Break on either side (but not pair)

exports.ZW = 20; // Zero-width space

exports.CM = 21; // Combining marks

exports.WJ = 22; // Word joiner

exports.H2 = 23; // Hangul LV

exports.H3 = 24; // Hangul LVT

exports.JL = 25; // Hangul L Jamo

exports.JV = 26; // Hangul V Jamo

exports.JT = 27; // Hangul T Jamo

exports.RI = 28; // Regional Indicator
// The following break classes are not handled by the pair table

exports.AI = 29; // Ambiguous (Alphabetic or Ideograph)

exports.BK = 30; // Break (mandatory)

exports.CB = 31; // Contingent break

exports.CJ = 32; // Conditional Japanese Starter

exports.CR = 33; // Carriage return

exports.LF = 34; // Line feed

exports.NL = 35; // Next line

exports.SA = 36; // South-East Asian

exports.SG = 37; // Surrogates

exports.SP = 38; // Space

exports.XX = 39; // Unknown

/***/ }),
/* 530 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var CI_BRK, CP_BRK, DI_BRK, IN_BRK, PR_BRK;
exports.DI_BRK = DI_BRK = 0; // Direct break opportunity

exports.IN_BRK = IN_BRK = 1; // Indirect break opportunity

exports.CI_BRK = CI_BRK = 2; // Indirect break opportunity for combining marks

exports.CP_BRK = CP_BRK = 3; // Prohibited break for combining marks

exports.PR_BRK = PR_BRK = 4; // Prohibited break
// table generated from http://www.unicode.org/reports/tr14/#Table2

exports.pairTable = [[PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, CP_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK], [IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK], [IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, DI_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, DI_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, PR_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK], [IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK], [DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK]];

/***/ }),
/* 531 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(Buffer) {

__webpack_require__(215);

__webpack_require__(192);

__webpack_require__(46);

/*
 * MIT LICENSE
 * Copyright (c) 2011 Devon Govett
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this
 * software and associated documentation files (the "Software"), to deal in the Software
 * without restriction, including without limitation the rights to use, copy, modify, merge,
 * publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons
 * to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or
 * substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
 * BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
 * DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var fs = __webpack_require__(53);

var zlib = __webpack_require__(244);

module.exports = /*#__PURE__*/function () {
  PNG.decode = function decode(path, fn) {
    return fs.readFile(path, function (err, file) {
      var png = new PNG(file);
      return png.decode(function (pixels) {
        return fn(pixels);
      });
    });
  };

  PNG.load = function load(path) {
    var file = fs.readFileSync(path);
    return new PNG(file);
  };

  function PNG(data) {
    var i;
    this.data = data;
    this.pos = 8; // Skip the default header

    this.palette = [];
    this.imgData = [];
    this.transparency = {};
    this.text = {};

    while (true) {
      var chunkSize = this.readUInt32();
      var section = '';

      for (i = 0; i < 4; i++) {
        section += String.fromCharCode(this.data[this.pos++]);
      }

      switch (section) {
        case 'IHDR':
          // we can grab  interesting values from here (like width, height, etc)
          this.width = this.readUInt32();
          this.height = this.readUInt32();
          this.bits = this.data[this.pos++];
          this.colorType = this.data[this.pos++];
          this.compressionMethod = this.data[this.pos++];
          this.filterMethod = this.data[this.pos++];
          this.interlaceMethod = this.data[this.pos++];
          break;

        case 'PLTE':
          this.palette = this.read(chunkSize);
          break;

        case 'IDAT':
          for (i = 0; i < chunkSize; i++) {
            this.imgData.push(this.data[this.pos++]);
          }

          break;

        case 'tRNS':
          // This chunk can only occur once and it must occur after the
          // PLTE chunk and before the IDAT chunk.
          this.transparency = {};

          switch (this.colorType) {
            case 3:
              // Indexed color, RGB. Each byte in this chunk is an alpha for
              // the palette index in the PLTE ("palette") chunk up until the
              // last non-opaque entry. Set up an array, stretching over all
              // palette entries which will be 0 (opaque) or 1 (transparent).
              this.transparency.indexed = this.read(chunkSize);
              var short = 255 - this.transparency.indexed.length;

              if (short > 0) {
                for (i = 0; i < short; i++) {
                  this.transparency.indexed.push(255);
                }
              }

              break;

            case 0:
              // Greyscale. Corresponding to entries in the PLTE chunk.
              // Grey is two bytes, range 0 .. (2 ^ bit-depth) - 1
              this.transparency.grayscale = this.read(chunkSize)[0];
              break;

            case 2:
              // True color with proper alpha channel.
              this.transparency.rgb = this.read(chunkSize);
              break;
          }

          break;

        case 'tEXt':
          var text = this.read(chunkSize);
          var index = text.indexOf(0);
          var key = String.fromCharCode.apply(String, text.slice(0, index));
          this.text[key] = String.fromCharCode.apply(String, text.slice(index + 1));
          break;

        case 'IEND':
          // we've got everything we need!
          switch (this.colorType) {
            case 0:
            case 3:
            case 4:
              this.colors = 1;
              break;

            case 2:
            case 6:
              this.colors = 3;
              break;
          }

          this.hasAlphaChannel = [4, 6].includes(this.colorType);
          var colors = this.colors + (this.hasAlphaChannel ? 1 : 0);
          this.pixelBitlength = this.bits * colors;

          switch (this.colors) {
            case 1:
              this.colorSpace = 'DeviceGray';
              break;

            case 3:
              this.colorSpace = 'DeviceRGB';
              break;
          }

          this.imgData = new Buffer(this.imgData);
          return;
          break;

        default:
          // unknown (or unimportant) section, skip it
          this.pos += chunkSize;
      }

      this.pos += 4; // Skip the CRC

      if (this.pos > this.data.length) {
        throw new Error('Incomplete or corrupt PNG file');
      }
    }
  }

  var _proto = PNG.prototype;

  _proto.read = function read(bytes) {
    var result = new Array(bytes);

    for (var _i = 0; _i < bytes; _i++) {
      result[_i] = this.data[this.pos++];
    }

    return result;
  };

  _proto.readUInt32 = function readUInt32() {
    var b1 = this.data[this.pos++] << 24;
    var b2 = this.data[this.pos++] << 16;
    var b3 = this.data[this.pos++] << 8;
    var b4 = this.data[this.pos++];
    return b1 | b2 | b3 | b4;
  };

  _proto.readUInt16 = function readUInt16() {
    var b1 = this.data[this.pos++] << 8;
    var b2 = this.data[this.pos++];
    return b1 | b2;
  };

  _proto.decodePixels = function decodePixels(fn) {
    var _this = this;

    return zlib.inflate(this.imgData, function (err, data) {
      if (err) {
        throw err;
      }

      var width = _this.width,
          height = _this.height;
      var pixelBytes = _this.pixelBitlength / 8;
      var pixels = new Buffer(width * height * pixelBytes);
      var length = data.length;
      var pos = 0;

      function pass(x0, y0, dx, dy, singlePass) {
        if (singlePass === void 0) {
          singlePass = false;
        }

        var w = Math.ceil((width - x0) / dx);
        var h = Math.ceil((height - y0) / dy);
        var scanlineLength = pixelBytes * w;
        var buffer = singlePass ? pixels : new Buffer(scanlineLength * h);
        var row = 0;
        var c = 0;

        while (row < h && pos < length) {
          var byte, col, i, left, upper;

          switch (data[pos++]) {
            case 0:
              // None
              for (i = 0; i < scanlineLength; i++) {
                buffer[c++] = data[pos++];
              }

              break;

            case 1:
              // Sub
              for (i = 0; i < scanlineLength; i++) {
                byte = data[pos++];
                left = i < pixelBytes ? 0 : buffer[c - pixelBytes];
                buffer[c++] = (byte + left) % 256;
              }

              break;

            case 2:
              // Up
              for (i = 0; i < scanlineLength; i++) {
                byte = data[pos++];
                col = (i - i % pixelBytes) / pixelBytes;
                upper = row && buffer[(row - 1) * scanlineLength + col * pixelBytes + i % pixelBytes];
                buffer[c++] = (upper + byte) % 256;
              }

              break;

            case 3:
              // Average
              for (i = 0; i < scanlineLength; i++) {
                byte = data[pos++];
                col = (i - i % pixelBytes) / pixelBytes;
                left = i < pixelBytes ? 0 : buffer[c - pixelBytes];
                upper = row && buffer[(row - 1) * scanlineLength + col * pixelBytes + i % pixelBytes];
                buffer[c++] = (byte + Math.floor((left + upper) / 2)) % 256;
              }

              break;

            case 4:
              // Paeth
              for (i = 0; i < scanlineLength; i++) {
                var paeth, upperLeft;
                byte = data[pos++];
                col = (i - i % pixelBytes) / pixelBytes;
                left = i < pixelBytes ? 0 : buffer[c - pixelBytes];

                if (row === 0) {
                  upper = upperLeft = 0;
                } else {
                  upper = buffer[(row - 1) * scanlineLength + col * pixelBytes + i % pixelBytes];
                  upperLeft = col && buffer[(row - 1) * scanlineLength + (col - 1) * pixelBytes + i % pixelBytes];
                }

                var p = left + upper - upperLeft;
                var pa = Math.abs(p - left);
                var pb = Math.abs(p - upper);
                var pc = Math.abs(p - upperLeft);

                if (pa <= pb && pa <= pc) {
                  paeth = left;
                } else if (pb <= pc) {
                  paeth = upper;
                } else {
                  paeth = upperLeft;
                }

                buffer[c++] = (byte + paeth) % 256;
              }

              break;

            default:
              throw new Error("Invalid filter algorithm: " + data[pos - 1]);
          }

          if (!singlePass) {
            var pixelsPos = ((y0 + row * dy) * width + x0) * pixelBytes;
            var bufferPos = row * scanlineLength;

            for (i = 0; i < w; i++) {
              for (var j = 0; j < pixelBytes; j++) {
                pixels[pixelsPos++] = buffer[bufferPos++];
              }

              pixelsPos += (dx - 1) * pixelBytes;
            }
          }

          row++;
        }
      }

      if (_this.interlaceMethod === 1) {
        /*
          1 6 4 6 2 6 4 6
          7 7 7 7 7 7 7 7
          5 6 5 6 5 6 5 6
          7 7 7 7 7 7 7 7
          3 6 4 6 3 6 4 6
          7 7 7 7 7 7 7 7
          5 6 5 6 5 6 5 6
          7 7 7 7 7 7 7 7
        */
        pass(0, 0, 8, 8); // 1

        pass(4, 0, 8, 8); // 2

        pass(0, 4, 4, 8); // 3

        pass(2, 0, 4, 4); // 4

        pass(0, 2, 2, 4); // 5

        pass(1, 0, 2, 2); // 6

        pass(0, 1, 1, 2); // 7
      } else {
        pass(0, 0, 1, 1, true);
      }

      return fn(pixels);
    });
  };

  _proto.decodePalette = function decodePalette() {
    var palette = this.palette;
    var length = palette.length;
    var transparency = this.transparency.indexed || [];
    var ret = new Buffer(transparency.length + length);
    var pos = 0;
    var c = 0;

    for (var _i2 = 0; _i2 < length; _i2 += 3) {
      var left;
      ret[pos++] = palette[_i2];
      ret[pos++] = palette[_i2 + 1];
      ret[pos++] = palette[_i2 + 2];
      ret[pos++] = (left = transparency[c++]) != null ? left : 255;
    }

    return ret;
  };

  _proto.copyToImageData = function copyToImageData(imageData, pixels) {
    var j, k;
    var colors = this.colors;
    var palette = null;
    var alpha = this.hasAlphaChannel;

    if (this.palette.length) {
      palette = this._decodedPalette || (this._decodedPalette = this.decodePalette());
      colors = 4;
      alpha = true;
    }

    var data = imageData.data || imageData;
    var length = data.length;
    var input = palette || pixels;
    var i = j = 0;

    if (colors === 1) {
      while (i < length) {
        k = palette ? pixels[i / 4] * 4 : j;
        var v = input[k++];
        data[i++] = v;
        data[i++] = v;
        data[i++] = v;
        data[i++] = alpha ? input[k++] : 255;
        j = k;
      }
    } else {
      while (i < length) {
        k = palette ? pixels[i / 4] * 4 : j;
        data[i++] = input[k++];
        data[i++] = input[k++];
        data[i++] = input[k++];
        data[i++] = alpha ? input[k++] : 255;
        j = k;
      }
    }
  };

  _proto.decode = function decode(fn) {
    var _this2 = this;

    var ret = new Buffer(this.width * this.height * 4);
    return this.decodePixels(function (pixels) {
      _this2.copyToImageData(ret, pixels);

      return fn(ret);
    });
  };

  return PNG;
}();
/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer))

/***/ }),
/* 532 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var isArray = __webpack_require__(0).isArray;

function typeName(bold, italics) {
	var type = 'normal';
	if (bold && italics) {
		type = 'bolditalics';
	} else if (bold) {
		type = 'bold';
	} else if (italics) {
		type = 'italics';
	}
	return type;
}

function FontProvider(fontDescriptors, pdfKitDoc) {
	this.fonts = {};
	this.pdfKitDoc = pdfKitDoc;
	this.fontCache = {};

	for (var font in fontDescriptors) {
		if (fontDescriptors.hasOwnProperty(font)) {
			var fontDef = fontDescriptors[font];

			this.fonts[font] = {
				normal: fontDef.normal,
				bold: fontDef.bold,
				italics: fontDef.italics,
				bolditalics: fontDef.bolditalics
			};
		}
	}
}

FontProvider.prototype.getFontType = function (bold, italics) {
	return typeName(bold, italics);
}

FontProvider.prototype.getFontFile = function (familyName, bold, italics) {
	var type = this.getFontType(bold, italics);
	if (!this.fonts[familyName] || !this.fonts[familyName][type]) {
		return null;
	}

	return this.fonts[familyName][type];
}

FontProvider.prototype.provideFont = function (familyName, bold, italics) {
	var type = this.getFontType(bold, italics);
	if (this.getFontFile(familyName, bold, italics) === null) {
		throw new Error('Font \'' + familyName + '\' in style \'' + type + '\' is not defined in the font section of the document definition.');
	}

	this.fontCache[familyName] = this.fontCache[familyName] || {};

	if (!this.fontCache[familyName][type]) {
		var def = this.fonts[familyName][type];
		if (!isArray(def)) {
			def = [def];
		}
		this.fontCache[familyName][type] = this.pdfKitDoc.font.apply(this.pdfKitDoc, def)._font;
	}

	return this.fontCache[familyName][type];
};

module.exports = FontProvider;


/***/ }),
/* 533 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var TraversalTracker = __webpack_require__(291);
var DocPreprocessor = __webpack_require__(534);
var DocMeasure = __webpack_require__(535);
var DocumentContext = __webpack_require__(293);
var PageElementWriter = __webpack_require__(537);
var ColumnCalculator = __webpack_require__(195);
var TableProcessor = __webpack_require__(539);
var Line = __webpack_require__(294);
var isString = __webpack_require__(0).isString;
var isArray = __webpack_require__(0).isArray;
var isUndefined = __webpack_require__(0).isUndefined;
var isNull = __webpack_require__(0).isNull;
var pack = __webpack_require__(0).pack;
var offsetVector = __webpack_require__(0).offsetVector;
var fontStringify = __webpack_require__(0).fontStringify;
var getNodeId = __webpack_require__(0).getNodeId;
var isFunction = __webpack_require__(0).isFunction;
var TextTools = __webpack_require__(194);
var StyleContextStack = __webpack_require__(292);
var isNumber = __webpack_require__(0).isNumber;

function addAll(target, otherArray) {
	otherArray.forEach(function (item) {
		target.push(item);
	});
}

/**
 * Creates an instance of LayoutBuilder - layout engine which turns document-definition-object
 * into a set of pages, lines, inlines and vectors ready to be rendered into a PDF
 *
 * @param {Object} pageSize - an object defining page width and height
 * @param {Object} pageMargins - an object defining top, left, right and bottom margins
 */
function LayoutBuilder(pageSize, pageMargins, imageMeasure, svgMeasure) {
	this.pageSize = pageSize;
	this.pageMargins = pageMargins;
	this.tracker = new TraversalTracker();
	this.imageMeasure = imageMeasure;
	this.svgMeasure = svgMeasure;
	this.tableLayouts = {};
}

LayoutBuilder.prototype.registerTableLayouts = function (tableLayouts) {
	this.tableLayouts = pack(this.tableLayouts, tableLayouts);
};

/**
 * Executes layout engine on document-definition-object and creates an array of pages
 * containing positioned Blocks, Lines and inlines
 *
 * @param {Object} docStructure document-definition-object
 * @param {Object} fontProvider font provider
 * @param {Object} styleDictionary dictionary with style definitions
 * @param {Object} defaultStyle default style definition
 * @return {Array} an array of pages
 */
LayoutBuilder.prototype.layoutDocument = function (docStructure, fontProvider, styleDictionary, defaultStyle, background, header, footer, images, watermark, pageBreakBeforeFct) {

	function addPageBreaksIfNecessary(linearNodeList, pages) {

		if (!isFunction(pageBreakBeforeFct)) {
			return false;
		}

		linearNodeList = linearNodeList.filter(function (node) {
			return node.positions.length > 0;
		});

		linearNodeList.forEach(function (node) {
			var nodeInfo = {};
			[
				'id', 'text', 'ul', 'ol', 'table', 'image', 'qr', 'canvas', 'svg', 'columns',
				'headlineLevel', 'style', 'pageBreak', 'pageOrientation',
				'width', 'height'
			].forEach(function (key) {
				if (node[key] !== undefined) {
					nodeInfo[key] = node[key];
				}
			});
			nodeInfo.startPosition = node.positions[0];
			nodeInfo.pageNumbers = node.positions.map(function (node) {
				return node.pageNumber;
			}).filter(function (element, position, array) {
				return array.indexOf(element) === position;
			});
			nodeInfo.pages = pages.length;
			nodeInfo.stack = isArray(node.stack);

			node.nodeInfo = nodeInfo;
		});

		for (var index = 0; index < linearNodeList.length; index++) {
			var node = linearNodeList[index];
			if (node.pageBreak !== 'before' && !node.pageBreakCalculated) {
				node.pageBreakCalculated = true;
				var pageNumber = node.nodeInfo.pageNumbers[0];
				var followingNodesOnPage = [];
				var nodesOnNextPage = [];
				var previousNodesOnPage = [];
				for (var ii = index + 1, l = linearNodeList.length; ii < l; ii++) {
					if (linearNodeList[ii].nodeInfo.pageNumbers.indexOf(pageNumber) > -1) {
						followingNodesOnPage.push(linearNodeList[ii].nodeInfo);
					}
					if (linearNodeList[ii].nodeInfo.pageNumbers.indexOf(pageNumber + 1) > -1) {
						nodesOnNextPage.push(linearNodeList[ii].nodeInfo);
					}
				}
				for (var ii = 0; ii < index; ii++) {
					if (linearNodeList[ii].nodeInfo.pageNumbers.indexOf(pageNumber) > -1) {
						previousNodesOnPage.push(linearNodeList[ii].nodeInfo);
					}
				}
				if (pageBreakBeforeFct(node.nodeInfo, followingNodesOnPage, nodesOnNextPage, previousNodesOnPage)) {
					node.pageBreak = 'before';
					return true;
				}
			}
		}

		return false;
	}

	this.docPreprocessor = new DocPreprocessor();
	this.docMeasure = new DocMeasure(fontProvider, styleDictionary, defaultStyle, this.imageMeasure, this.svgMeasure, this.tableLayouts, images);


	function resetXYs(result) {
		result.linearNodeList.forEach(function (node) {
			node.resetXY();
		});
	}

	var result = this.tryLayoutDocument(docStructure, fontProvider, styleDictionary, defaultStyle, background, header, footer, images, watermark);
	while (addPageBreaksIfNecessary(result.linearNodeList, result.pages)) {
		resetXYs(result);
		result = this.tryLayoutDocument(docStructure, fontProvider, styleDictionary, defaultStyle, background, header, footer, images, watermark);
	}

	return result.pages;
};

LayoutBuilder.prototype.tryLayoutDocument = function (docStructure, fontProvider, styleDictionary, defaultStyle, background, header, footer, images, watermark, pageBreakBeforeFct) {

	this.linearNodeList = [];
	docStructure = this.docPreprocessor.preprocessDocument(docStructure);
	docStructure = this.docMeasure.measureDocument(docStructure);

	this.writer = new PageElementWriter(
		new DocumentContext(this.pageSize, this.pageMargins), this.tracker);

	var _this = this;
	this.writer.context().tracker.startTracking('pageAdded', function () {
		_this.addBackground(background);
	});

	this.addBackground(background);
	this.processNode(docStructure);
	this.addHeadersAndFooters(header, footer);
	if (watermark != null) {
		this.addWatermark(watermark, fontProvider, defaultStyle);
	}

	return { pages: this.writer.context().pages, linearNodeList: this.linearNodeList };
};


LayoutBuilder.prototype.addBackground = function (background) {
	var backgroundGetter = isFunction(background) ? background : function () {
		return background;
	};

	var context = this.writer.context();
	var pageSize = context.getCurrentPage().pageSize;

	var pageBackground = backgroundGetter(context.page + 1, pageSize);

	if (pageBackground) {
		this.writer.beginUnbreakableBlock(pageSize.width, pageSize.height);
		pageBackground = this.docPreprocessor.preprocessDocument(pageBackground);
		this.processNode(this.docMeasure.measureDocument(pageBackground));
		this.writer.commitUnbreakableBlock(0, 0);
		context.backgroundLength[context.page] += pageBackground.positions.length;
	}
};

LayoutBuilder.prototype.addStaticRepeatable = function (headerOrFooter, sizeFunction) {
	this.addDynamicRepeatable(function () {
		return JSON.parse(JSON.stringify(headerOrFooter)); // copy to new object
	}, sizeFunction);
};

LayoutBuilder.prototype.addDynamicRepeatable = function (nodeGetter, sizeFunction) {
	var pages = this.writer.context().pages;

	for (var pageIndex = 0, l = pages.length; pageIndex < l; pageIndex++) {
		this.writer.context().page = pageIndex;

		var node = nodeGetter(pageIndex + 1, l, this.writer.context().pages[pageIndex].pageSize);

		if (node) {
			var sizes = sizeFunction(this.writer.context().getCurrentPage().pageSize, this.pageMargins);
			this.writer.beginUnbreakableBlock(sizes.width, sizes.height);
			node = this.docPreprocessor.preprocessDocument(node);
			this.processNode(this.docMeasure.measureDocument(node));
			this.writer.commitUnbreakableBlock(sizes.x, sizes.y);
		}
	}
};

LayoutBuilder.prototype.addHeadersAndFooters = function (header, footer) {
	var headerSizeFct = function (pageSize, pageMargins) {
		return {
			x: 0,
			y: 0,
			width: pageSize.width,
			height: pageMargins.top
		};
	};

	var footerSizeFct = function (pageSize, pageMargins) {
		return {
			x: 0,
			y: pageSize.height - pageMargins.bottom,
			width: pageSize.width,
			height: pageMargins.bottom
		};
	};

	if (isFunction(header)) {
		this.addDynamicRepeatable(header, headerSizeFct);
	} else if (header) {
		this.addStaticRepeatable(header, headerSizeFct);
	}

	if (isFunction(footer)) {
		this.addDynamicRepeatable(footer, footerSizeFct);
	} else if (footer) {
		this.addStaticRepeatable(footer, footerSizeFct);
	}
};

LayoutBuilder.prototype.addWatermark = function (watermark, fontProvider, defaultStyle) {
	if (isString(watermark)) {
		watermark = { 'text': watermark };
	}

	if (!watermark.text) { // empty watermark text
		return;
	}

	watermark.font = watermark.font || defaultStyle.font || 'Roboto';
	watermark.fontSize = watermark.fontSize || 'auto';
	watermark.color = watermark.color || 'black';
	watermark.opacity = isNumber(watermark.opacity) ? watermark.opacity : 0.6;
	watermark.bold = watermark.bold || false;
	watermark.italics = watermark.italics || false;
	watermark.angle = !isUndefined(watermark.angle) && !isNull(watermark.angle) ? watermark.angle : null;

	if (watermark.angle === null) {
		watermark.angle = Math.atan2(this.pageSize.height, this.pageSize.width) * -180 / Math.PI;
	}

	if (watermark.fontSize === 'auto') {
		watermark.fontSize = getWatermarkFontSize(this.pageSize, watermark, fontProvider);
	}

	var watermarkObject = {
		text: watermark.text,
		font: fontProvider.provideFont(watermark.font, watermark.bold, watermark.italics),
		fontSize: watermark.fontSize,
		color: watermark.color,
		opacity: watermark.opacity,
		angle: watermark.angle
	};

	watermarkObject._size = getWatermarkSize(watermark, fontProvider);

	var pages = this.writer.context().pages;
	for (var i = 0, l = pages.length; i < l; i++) {
		pages[i].watermark = watermarkObject;
	}

	function getWatermarkSize(watermark, fontProvider) {
		var textTools = new TextTools(fontProvider);
		var styleContextStack = new StyleContextStack(null, { font: watermark.font, bold: watermark.bold, italics: watermark.italics });

		styleContextStack.push({
			fontSize: watermark.fontSize
		});

		var size = textTools.sizeOfString(watermark.text, styleContextStack);
		var rotatedSize = textTools.sizeOfRotatedText(watermark.text, watermark.angle, styleContextStack);

		return { size: size, rotatedSize: rotatedSize };
	}

	function getWatermarkFontSize(pageSize, watermark, fontProvider) {
		var textTools = new TextTools(fontProvider);
		var styleContextStack = new StyleContextStack(null, { font: watermark.font, bold: watermark.bold, italics: watermark.italics });
		var rotatedSize;

		/**
		 * Binary search the best font size.
		 * Initial bounds [0, 1000]
		 * Break when range < 1
		 */
		var a = 0;
		var b = 1000;
		var c = (a + b) / 2;
		while (Math.abs(a - b) > 1) {
			styleContextStack.push({
				fontSize: c
			});
			rotatedSize = textTools.sizeOfRotatedText(watermark.text, watermark.angle, styleContextStack);
			if (rotatedSize.width > pageSize.width) {
				b = c;
				c = (a + b) / 2;
			} else if (rotatedSize.width < pageSize.width) {
				if (rotatedSize.height > pageSize.height) {
					b = c;
					c = (a + b) / 2;
				} else {
					a = c;
					c = (a + b) / 2;
				}
			}
			styleContextStack.pop();
		}
		/*
		 End binary search
		 */
		return c;
	}
};

function decorateNode(node) {
	var x = node.x, y = node.y;
	node.positions = [];

	if (isArray(node.canvas)) {
		node.canvas.forEach(function (vector) {
			var x = vector.x, y = vector.y, x1 = vector.x1, y1 = vector.y1, x2 = vector.x2, y2 = vector.y2;
			vector.resetXY = function () {
				vector.x = x;
				vector.y = y;
				vector.x1 = x1;
				vector.y1 = y1;
				vector.x2 = x2;
				vector.y2 = y2;
			};
		});
	}

	node.resetXY = function () {
		node.x = x;
		node.y = y;
		if (isArray(node.canvas)) {
			node.canvas.forEach(function (vector) {
				vector.resetXY();
			});
		}
	};
}

LayoutBuilder.prototype.processNode = function (node) {
	var self = this;

	this.linearNodeList.push(node);
	decorateNode(node);

	applyMargins(function () {
		var unbreakable = node.unbreakable;
		if (unbreakable) {
			self.writer.beginUnbreakableBlock();
		}

		var absPosition = node.absolutePosition;
		if (absPosition) {
			self.writer.context().beginDetachedBlock();
			self.writer.context().moveTo(absPosition.x || 0, absPosition.y || 0);
		}

		var relPosition = node.relativePosition;
		if (relPosition) {
			self.writer.context().beginDetachedBlock();
			self.writer.context().moveToRelative(relPosition.x || 0, relPosition.y || 0);
		}

		if (node.stack) {
			self.processVerticalContainer(node);
		} else if (node.columns) {
			self.processColumns(node);
		} else if (node.ul) {
			self.processList(false, node);
		} else if (node.ol) {
			self.processList(true, node);
		} else if (node.table) {
			self.processTable(node);
		} else if (node.text !== undefined) {
			self.processLeaf(node);
		} else if (node.toc) {
			self.processToc(node);
		} else if (node.image) {
			self.processImage(node);
		} else if (node.svg) {
			self.processSVG(node);
		} else if (node.canvas) {
			self.processCanvas(node);
		} else if (node.qr) {
			self.processQr(node);
		} else if (!node._span) {
			throw 'Unrecognized document structure: ' + JSON.stringify(node, fontStringify);
		}

		if (absPosition || relPosition) {
			self.writer.context().endDetachedBlock();
		}

		if (unbreakable) {
			self.writer.commitUnbreakableBlock();
		}
	});

	function applyMargins(callback) {
		var margin = node._margin;

		if (node.pageBreak === 'before') {
			self.writer.moveToNextPage(node.pageOrientation);
		} else if (node.pageBreak === 'beforeOdd') {
			self.writer.moveToNextPage(node.pageOrientation);
			if ((self.writer.context().page + 1) % 2 === 1) {
				self.writer.moveToNextPage(node.pageOrientation);
			}
		} else if (node.pageBreak === 'beforeEven') {
			self.writer.moveToNextPage(node.pageOrientation);
			if ((self.writer.context().page + 1) % 2 === 0) {
				self.writer.moveToNextPage(node.pageOrientation);
			}
		}

		if (margin) {
			self.writer.context().moveDown(margin[1]);
			self.writer.context().addMargin(margin[0], margin[2]);
		}

		callback();

		if (margin) {
			self.writer.context().addMargin(-margin[0], -margin[2]);
			self.writer.context().moveDown(margin[3]);
		}

		if (node.pageBreak === 'after') {
			self.writer.moveToNextPage(node.pageOrientation);
		} else if (node.pageBreak === 'afterOdd') {
			self.writer.moveToNextPage(node.pageOrientation);
			if ((self.writer.context().page + 1) % 2 === 1) {
				self.writer.moveToNextPage(node.pageOrientation);
			}
		} else if (node.pageBreak === 'afterEven') {
			self.writer.moveToNextPage(node.pageOrientation);
			if ((self.writer.context().page + 1) % 2 === 0) {
				self.writer.moveToNextPage(node.pageOrientation);
			}
		}
	}
};

// vertical container
LayoutBuilder.prototype.processVerticalContainer = function (node) {
	var self = this;
	node.stack.forEach(function (item) {
		self.processNode(item);
		addAll(node.positions, item.positions);

		//TODO: paragraph gap
	});
};

// columns
LayoutBuilder.prototype.processColumns = function (columnNode) {
	var columns = columnNode.columns;
	var availableWidth = this.writer.context().availableWidth;
	var gaps = gapArray(columnNode._gap);

	if (gaps) {
		availableWidth -= (gaps.length - 1) * columnNode._gap;
	}

	ColumnCalculator.buildColumnWidths(columns, availableWidth);
	var result = this.processRow(columns, columns, gaps);
	addAll(columnNode.positions, result.positions);


	function gapArray(gap) {
		if (!gap) {
			return null;
		}

		var gaps = [];
		gaps.push(0);

		for (var i = columns.length - 1; i > 0; i--) {
			gaps.push(gap);
		}

		return gaps;
	}
};

LayoutBuilder.prototype.processRow = function (columns, widths, gaps, tableBody, tableRow, height) {
	var self = this;
	var pageBreaks = [], positions = [];

	this.tracker.auto('pageChanged', storePageBreakData, function () {
		widths = widths || columns;

		self.writer.context().beginColumnGroup();

		for (var i = 0, l = columns.length; i < l; i++) {
			var column = columns[i];
			var width = widths[i]._calcWidth;
			var leftOffset = colLeftOffset(i);

			if (column.colSpan && column.colSpan > 1) {
				for (var j = 1; j < column.colSpan; j++) {
					width += widths[++i]._calcWidth + gaps[i];
				}
			}

			self.writer.context().beginColumn(width, leftOffset, getEndingCell(column, i));
			if (!column._span) {
				self.processNode(column);
				addAll(positions, column.positions);
			} else if (column._columnEndingContext) {
				// row-span ending
				self.writer.context().markEnding(column);
			}
		}

		self.writer.context().completeColumnGroup(height);
	});

	return { pageBreaks: pageBreaks, positions: positions };

	function storePageBreakData(data) {
		var pageDesc;

		for (var i = 0, l = pageBreaks.length; i < l; i++) {
			var desc = pageBreaks[i];
			if (desc.prevPage === data.prevPage) {
				pageDesc = desc;
				break;
			}
		}

		if (!pageDesc) {
			pageDesc = data;
			pageBreaks.push(pageDesc);
		}
		pageDesc.prevY = Math.max(pageDesc.prevY, data.prevY);
		pageDesc.y = Math.min(pageDesc.y, data.y);
	}

	function colLeftOffset(i) {
		if (gaps && gaps.length > i) {
			return gaps[i];
		}
		return 0;
	}

	function getEndingCell(column, columnIndex) {
		if (column.rowSpan && column.rowSpan > 1) {
			var endingRow = tableRow + column.rowSpan - 1;
			if (endingRow >= tableBody.length) {
				throw 'Row span for column ' + columnIndex + ' (with indexes starting from 0) exceeded row count';
			}
			return tableBody[endingRow][columnIndex];
		}

		return null;
	}
};

// lists
LayoutBuilder.prototype.processList = function (orderedList, node) {
	var self = this,
		items = orderedList ? node.ol : node.ul,
		gapSize = node._gapSize;

	this.writer.context().addMargin(gapSize.width);

	var nextMarker;
	this.tracker.auto('lineAdded', addMarkerToFirstLeaf, function () {
		items.forEach(function (item) {
			nextMarker = item.listMarker;
			self.processNode(item);
			addAll(node.positions, item.positions);
		});
	});

	this.writer.context().addMargin(-gapSize.width);

	function addMarkerToFirstLeaf(line) {
		// I'm not very happy with the way list processing is implemented
		// (both code and algorithm should be rethinked)
		if (nextMarker) {
			var marker = nextMarker;
			nextMarker = null;

			if (marker.canvas) {
				var vector = marker.canvas[0];

				offsetVector(vector, -marker._minWidth, 0);
				self.writer.addVector(vector);
			} else if (marker._inlines) {
				var markerLine = new Line(self.pageSize.width);
				markerLine.addInline(marker._inlines[0]);
				markerLine.x = -marker._minWidth;
				markerLine.y = line.getAscenderHeight() - markerLine.getAscenderHeight();
				self.writer.addLine(markerLine, true);
			}
		}
	}
};

// tables
LayoutBuilder.prototype.processTable = function (tableNode) {
	var processor = new TableProcessor(tableNode);

	processor.beginTable(this.writer);

	var rowHeights = tableNode.table.heights;
	for (var i = 0, l = tableNode.table.body.length; i < l; i++) {
		processor.beginRow(i, this.writer);

		var height;
		if (isFunction(rowHeights)) {
			height = rowHeights(i);
		} else if (isArray(rowHeights)) {
			height = rowHeights[i];
		} else {
			height = rowHeights;
		}

		if (height === 'auto') {
			height = undefined;
		}

		var result = this.processRow(tableNode.table.body[i], tableNode.table.widths, tableNode._offsets.offsets, tableNode.table.body, i, height);
		addAll(tableNode.positions, result.positions);

		processor.endRow(i, this.writer, result.pageBreaks);
	}

	processor.endTable(this.writer);
};

// leafs (texts)
LayoutBuilder.prototype.processLeaf = function (node) {
	var line = this.buildNextLine(node);
	if (line && (node.tocItem || node.id)) {
		line._node = node;
	}
	var currentHeight = (line) ? line.getHeight() : 0;
	var maxHeight = node.maxHeight || -1;

	if (line) {
		var nodeId = getNodeId(node);
		if (nodeId) {
			line.id = nodeId;
		}
	}

	if (node._tocItemRef) {
		line._pageNodeRef = node._tocItemRef;
	}

	if (node._pageRef) {
		line._pageNodeRef = node._pageRef._nodeRef;
	}

	if (line && line.inlines && isArray(line.inlines)) {
		for (var i = 0, l = line.inlines.length; i < l; i++) {
			if (line.inlines[i]._tocItemRef) {
				line.inlines[i]._pageNodeRef = line.inlines[i]._tocItemRef;
			}

			if (line.inlines[i]._pageRef) {
				line.inlines[i]._pageNodeRef = line.inlines[i]._pageRef._nodeRef;
			}
		}
	}

	while (line && (maxHeight === -1 || currentHeight < maxHeight)) {
		var positions = this.writer.addLine(line);
		node.positions.push(positions);
		line = this.buildNextLine(node);
		if (line) {
			currentHeight += line.getHeight();
		}
	}
};

LayoutBuilder.prototype.processToc = function (node) {
	if (node.toc.title) {
		this.processNode(node.toc.title);
	}
	if (node.toc._table) {
		this.processNode(node.toc._table);
	}
};

LayoutBuilder.prototype.buildNextLine = function (textNode) {

	function cloneInline(inline) {
		var newInline = inline.constructor();
		for (var key in inline) {
			newInline[key] = inline[key];
		}
		return newInline;
	}

	if (!textNode._inlines || textNode._inlines.length === 0) {
		return null;
	}

	var line = new Line(this.writer.context().availableWidth);
	var textTools = new TextTools(null);

	var isForceContinue = false;
	while (textNode._inlines && textNode._inlines.length > 0 &&
		(line.hasEnoughSpaceForInline(textNode._inlines[0], textNode._inlines.slice(1)) || isForceContinue)) {
		var isHardWrap = false;
		var inline = textNode._inlines.shift();
		isForceContinue = false;

		if (!inline.noWrap && inline.text.length > 1 && inline.width > line.getAvailableWidth()) {
			var widthPerChar = inline.width / inline.text.length;
			var maxChars = Math.floor(line.getAvailableWidth() / widthPerChar);
			if (maxChars < 1) {
				maxChars = 1;
			}
			if (maxChars < inline.text.length) {
				var newInline = cloneInline(inline);

				newInline.text = inline.text.substr(maxChars);
				inline.text = inline.text.substr(0, maxChars);

				newInline.width = textTools.widthOfString(newInline.text, newInline.font, newInline.fontSize, newInline.characterSpacing, newInline.fontFeatures);
				inline.width = textTools.widthOfString(inline.text, inline.font, inline.fontSize, inline.characterSpacing, inline.fontFeatures);

				textNode._inlines.unshift(newInline);
				isHardWrap = true;
			}
		}

		line.addInline(inline);

		isForceContinue = inline.noNewLine && !isHardWrap;
	}

	line.lastLineInParagraph = textNode._inlines.length === 0;

	return line;
};

// images
LayoutBuilder.prototype.processImage = function (node) {
	var position = this.writer.addImage(node);
	node.positions.push(position);
};

LayoutBuilder.prototype.processSVG = function (node) {
	var position = this.writer.addSVG(node);
	node.positions.push(position);
};

LayoutBuilder.prototype.processCanvas = function (node) {
	var height = node._minHeight;

	if (node.absolutePosition === undefined && this.writer.context().availableHeight < height) {
		// TODO: support for canvas larger than a page
		// TODO: support for other overflow methods

		this.writer.moveToNextPage();
	}

	this.writer.alignCanvas(node);

	node.canvas.forEach(function (vector) {
		var position = this.writer.addVector(vector);
		node.positions.push(position);
	}, this);

	this.writer.context().moveDown(height);
};

LayoutBuilder.prototype.processQr = function (node) {
	var position = this.writer.addQr(node);
	node.positions.push(position);
};

module.exports = LayoutBuilder;


/***/ }),
/* 534 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(Buffer) {

var isString = __webpack_require__(0).isString;
var isNumber = __webpack_require__(0).isNumber;
var isBoolean = __webpack_require__(0).isBoolean;
var isArray = __webpack_require__(0).isArray;
var isUndefined = __webpack_require__(0).isUndefined;
var fontStringify = __webpack_require__(0).fontStringify;

function DocPreprocessor() {

}

DocPreprocessor.prototype.preprocessDocument = function (docStructure) {
	this.parentNode = null;
	this.tocs = [];
	this.nodeReferences = [];
	return this.preprocessNode(docStructure);
};

DocPreprocessor.prototype.preprocessNode = function (node) {
	// expand shortcuts and casting values
	if (isArray(node)) {
		node = { stack: node };
	} else if (isString(node)) {
		node = { text: node };
	} else if (isNumber(node) || isBoolean(node)) {
		node = { text: node.toString() };
	} else if (node === undefined || node === null) {
		node = { text: '' };
	} else if (Object.keys(node).length === 0) { // empty object
		node = { text: '' };
	} else if ('text' in node && (node.text === undefined || node.text === null)) {
		node.text = '';
	}

	if (node.columns) {
		return this.preprocessColumns(node);
	} else if (node.stack) {
		return this.preprocessVerticalContainer(node);
	} else if (node.ul) {
		return this.preprocessList(node);
	} else if (node.ol) {
		return this.preprocessList(node);
	} else if (node.table) {
		return this.preprocessTable(node);
	} else if (node.text !== undefined) {
		return this.preprocessText(node);
	} else if (node.toc) {
		return this.preprocessToc(node);
	} else if (node.image) {
		return this.preprocessImage(node);
	} else if (node.svg) {
		return this.preprocessSVG(node);
	} else if (node.canvas) {
		return this.preprocessCanvas(node);
	} else if (node.qr) {
		return this.preprocessQr(node);
	} else if (node.pageReference || node.textReference) {
		return this.preprocessText(node);
	} else {
		throw 'Unrecognized document structure: ' + JSON.stringify(node, fontStringify);
	}
};

DocPreprocessor.prototype.preprocessColumns = function (node) {
	var columns = node.columns;

	for (var i = 0, l = columns.length; i < l; i++) {
		columns[i] = this.preprocessNode(columns[i]);
	}

	return node;
};

DocPreprocessor.prototype.preprocessVerticalContainer = function (node) {
	var items = node.stack;

	for (var i = 0, l = items.length; i < l; i++) {
		items[i] = this.preprocessNode(items[i]);
	}

	return node;
};

DocPreprocessor.prototype.preprocessList = function (node) {
	var items = node.ul || node.ol;

	for (var i = 0, l = items.length; i < l; i++) {
		items[i] = this.preprocessNode(items[i]);
	}

	return node;
};

DocPreprocessor.prototype.preprocessTable = function (node) {
	var col, row, cols, rows;

	for (col = 0, cols = node.table.body[0].length; col < cols; col++) {
		for (row = 0, rows = node.table.body.length; row < rows; row++) {
			var rowData = node.table.body[row];
			var data = rowData[col];
			if (data !== undefined) {
				if (data === null) { // transform to object
					data = '';
				}
				if (!data._span) {
					rowData[col] = this.preprocessNode(data);
				}
			}
		}
	}

	return node;
};

DocPreprocessor.prototype.preprocessText = function (node) {
	if (node.tocItem) {
		if (!isArray(node.tocItem)) {
			node.tocItem = [node.tocItem];
		}

		for (var i = 0, l = node.tocItem.length; i < l; i++) {
			if (!isString(node.tocItem[i])) {
				node.tocItem[i] = '_default_';
			}

			var tocItemId = node.tocItem[i];

			if (!this.tocs[tocItemId]) {
				this.tocs[tocItemId] = { toc: { _items: [], _pseudo: true } };
			}

			if (!node.id) {
				node.id = 'toc-' + tocItemId + '-' + this.tocs[tocItemId].toc._items.length;
			}

			var tocItemRef = {
				_nodeRef: this._getNodeForNodeRef(node),
				_textNodeRef: node
			};
			this.tocs[tocItemId].toc._items.push(tocItemRef);
		}
	}

	if (node.id) {
		if (this.nodeReferences[node.id]) {
			if (!this.nodeReferences[node.id]._pseudo) {
				throw "Node id '" + node.id + "' already exists";
			}

			this.nodeReferences[node.id]._nodeRef = this._getNodeForNodeRef(node);
			this.nodeReferences[node.id]._textNodeRef = node;
			this.nodeReferences[node.id]._pseudo = false;
		} else {
			this.nodeReferences[node.id] = {
				_nodeRef: this._getNodeForNodeRef(node),
				_textNodeRef: node
			};
		}
	}

	if (node.pageReference) {
		if (!this.nodeReferences[node.pageReference]) {
			this.nodeReferences[node.pageReference] = {
				_nodeRef: {},
				_textNodeRef: {},
				_pseudo: true
			};
		}
		node.text = '00000';
		node.linkToDestination = node.pageReference;
		node._pageRef = this.nodeReferences[node.pageReference];
	}

	if (node.textReference) {
		if (!this.nodeReferences[node.textReference]) {
			this.nodeReferences[node.textReference] = { _nodeRef: {}, _pseudo: true };
		}

		node.text = '';
		node.linkToDestination = node.textReference;
		node._textRef = this.nodeReferences[node.textReference];
	}

	if (node.text && node.text.text) {
		node.text = [this.preprocessNode(node.text)];
	} else if (isArray(node.text)) {
		var isSetParentNode = false;
		if (this.parentNode === null) {
			this.parentNode = node;
			isSetParentNode = true;
		}

		for (var i = 0, l = node.text.length; i < l; i++) {
			node.text[i] = this.preprocessNode(node.text[i]);
		}

		if (isSetParentNode) {
			this.parentNode = null;
		}
	}

	return node;
};

DocPreprocessor.prototype.preprocessToc = function (node) {
	if (!node.toc.id) {
		node.toc.id = '_default_';
	}

	node.toc.title = node.toc.title ? this.preprocessNode(node.toc.title) : null;
	node.toc._items = [];

	if (this.tocs[node.toc.id]) {
		if (!this.tocs[node.toc.id].toc._pseudo) {
			throw "TOC '" + node.toc.id + "' already exists";
		}

		node.toc._items = this.tocs[node.toc.id].toc._items;
	}

	this.tocs[node.toc.id] = node;

	return node;
};

DocPreprocessor.prototype.preprocessImage = function (node) {
	if (!isUndefined(node.image.type) && !isUndefined(node.image.data) && (node.image.type === 'Buffer') && isArray(node.image.data)) {
		node.image = Buffer.from(node.image.data);
	}
	return node;
};

DocPreprocessor.prototype.preprocessSVG = function (node) {
	return node;
};

DocPreprocessor.prototype.preprocessCanvas = function (node) {
	return node;
};

DocPreprocessor.prototype.preprocessQr = function (node) {
	return node;
};

DocPreprocessor.prototype._getNodeForNodeRef = function (node) {
	if (this.parentNode) {
		return this.parentNode;
	}

	return node;
}

module.exports = DocPreprocessor;

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer))

/***/ }),
/* 535 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/*eslint no-unused-vars: ["error", {"args": "none"}]*/



var TextTools = __webpack_require__(194);
var StyleContextStack = __webpack_require__(292);
var ColumnCalculator = __webpack_require__(195);
var isString = __webpack_require__(0).isString;
var isNumber = __webpack_require__(0).isNumber;
var isObject = __webpack_require__(0).isObject;
var isArray = __webpack_require__(0).isArray;
var fontStringify = __webpack_require__(0).fontStringify;
var getNodeId = __webpack_require__(0).getNodeId;
var pack = __webpack_require__(0).pack;
var qrEncoder = __webpack_require__(536);

/**
 * @private
 */
function DocMeasure(fontProvider, styleDictionary, defaultStyle, imageMeasure, svgMeasure, tableLayouts, images) {
	this.textTools = new TextTools(fontProvider);
	this.styleStack = new StyleContextStack(styleDictionary, defaultStyle);
	this.imageMeasure = imageMeasure;
	this.svgMeasure = svgMeasure;
	this.tableLayouts = tableLayouts;
	this.images = images;
	this.autoImageIndex = 1;
}

/**
 * Measures all nodes and sets min/max-width properties required for the second
 * layout-pass.
 * @param  {Object} docStructure document-definition-object
 * @return {Object}              document-measurement-object
 */
DocMeasure.prototype.measureDocument = function (docStructure) {
	return this.measureNode(docStructure);
};

DocMeasure.prototype.measureNode = function (node) {

	var self = this;

	return this.styleStack.auto(node, function () {
		// TODO: refactor + rethink whether this is the proper way to handle margins
		node._margin = getNodeMargin(node);

		if (node.columns) {
			return extendMargins(self.measureColumns(node));
		} else if (node.stack) {
			return extendMargins(self.measureVerticalContainer(node));
		} else if (node.ul) {
			return extendMargins(self.measureUnorderedList(node));
		} else if (node.ol) {
			return extendMargins(self.measureOrderedList(node));
		} else if (node.table) {
			return extendMargins(self.measureTable(node));
		} else if (node.text !== undefined) {
			return extendMargins(self.measureLeaf(node));
		} else if (node.toc) {
			return extendMargins(self.measureToc(node));
		} else if (node.image) {
			return extendMargins(self.measureImage(node));
		} else if (node.svg) {
			return extendMargins(self.measureSVG(node));
		} else if (node.canvas) {
			return extendMargins(self.measureCanvas(node));
		} else if (node.qr) {
			return extendMargins(self.measureQr(node));
		} else {
			throw 'Unrecognized document structure: ' + JSON.stringify(node, fontStringify);
		}
	});

	function extendMargins(node) {
		var margin = node._margin;

		if (margin) {
			node._minWidth += margin[0] + margin[2];
			node._maxWidth += margin[0] + margin[2];
		}

		return node;
	}

	function getNodeMargin() {

		function processSingleMargins(node, currentMargin) {
			if (node.marginLeft || node.marginTop || node.marginRight || node.marginBottom) {
				return [
					node.marginLeft || currentMargin[0] || 0,
					node.marginTop || currentMargin[1] || 0,
					node.marginRight || currentMargin[2] || 0,
					node.marginBottom || currentMargin[3] || 0
				];
			}
			return currentMargin;
		}

		function flattenStyleArray(styleArray) {
			var flattenedStyles = {};
			for (var i = styleArray.length - 1; i >= 0; i--) {
				var styleName = styleArray[i];
				var style = self.styleStack.styleDictionary[styleName];
				for (var key in style) {
					if (style.hasOwnProperty(key)) {
						flattenedStyles[key] = style[key];
					}
				}
			}
			return flattenedStyles;
		}

		function convertMargin(margin) {
			if (isNumber(margin)) {
				margin = [margin, margin, margin, margin];
			} else if (isArray(margin)) {
				if (margin.length === 2) {
					margin = [margin[0], margin[1], margin[0], margin[1]];
				}
			}
			return margin;
		}

		var margin = [undefined, undefined, undefined, undefined];

		if (node.style) {
			var styleArray = isArray(node.style) ? node.style : [node.style];
			var flattenedStyleArray = flattenStyleArray(styleArray);

			if (flattenedStyleArray) {
				margin = processSingleMargins(flattenedStyleArray, margin);
			}

			if (flattenedStyleArray.margin) {
				margin = convertMargin(flattenedStyleArray.margin);
			}
		}

		margin = processSingleMargins(node, margin);

		if (node.margin) {
			margin = convertMargin(node.margin);
		}

		if (margin[0] === undefined && margin[1] === undefined && margin[2] === undefined && margin[3] === undefined) {
			return null;
		} else {
			return margin;
		}
	}
};

DocMeasure.prototype.convertIfBase64Image = function (node) {
	if (/^data:image\/(jpeg|jpg|png);base64,/.test(node.image)) {
		var label = '$$pdfmake$$' + this.autoImageIndex++;
		this.images[label] = node.image;
		node.image = label;
	}
};

DocMeasure.prototype.measureImageWithDimensions = function (node, dimensions) {
	if (node.fit) {
		var factor = (dimensions.width / dimensions.height > node.fit[0] / node.fit[1]) ? node.fit[0] / dimensions.width : node.fit[1] / dimensions.height;
		node._width = node._minWidth = node._maxWidth = dimensions.width * factor;
		node._height = dimensions.height * factor;
	} else {
		node._width = node._minWidth = node._maxWidth = node.width || dimensions.width;
		node._height = node.height || (dimensions.height * node._width / dimensions.width);

		if (isNumber(node.maxWidth) && node.maxWidth < node._width) {
			node._width = node._minWidth = node._maxWidth = node.maxWidth;
			node._height = node._width * dimensions.height / dimensions.width;
		}

		if (isNumber(node.maxHeight) && node.maxHeight < node._height) {
			node._height = node.maxHeight;
			node._width = node._minWidth = node._maxWidth = node._height * dimensions.width / dimensions.height;
		}

		if (isNumber(node.minWidth) && node.minWidth > node._width) {
			node._width = node._minWidth = node._maxWidth = node.minWidth;
			node._height = node._width * dimensions.height / dimensions.width;
		}

		if (isNumber(node.minHeight) && node.minHeight > node._height) {
			node._height = node.minHeight;
			node._width = node._minWidth = node._maxWidth = node._height * dimensions.width / dimensions.height;
		}
	}

	node._alignment = this.styleStack.getProperty('alignment');
};

DocMeasure.prototype.measureImage = function (node) {
	if (this.images) {
		this.convertIfBase64Image(node);
	}

	var dimensions = this.imageMeasure.measureImage(node.image);

	this.measureImageWithDimensions(node, dimensions);

	return node;
};

DocMeasure.prototype.measureSVG = function (node) {

	var dimensions = this.svgMeasure.measureSVG(node.svg);

	this.measureImageWithDimensions(node, dimensions);

	node.font = this.styleStack.getProperty('font');

	// scale SVG based on final dimension
	node.svg = this.svgMeasure.writeDimensions(node.svg, {
		width: node._width,
		height: node._height
	});

	return node;
};

DocMeasure.prototype.measureLeaf = function (node) {

	if (node._textRef && node._textRef._textNodeRef.text) {
		node.text = node._textRef._textNodeRef.text;
	}

	// Make sure style properties of the node itself are considered when building inlines.
	// We could also just pass [node] to buildInlines, but that fails for bullet points.
	var styleStack = this.styleStack.clone();
	styleStack.push(node);

	var data = this.textTools.buildInlines(node.text, styleStack);

	node._inlines = data.items;
	node._minWidth = data.minWidth;
	node._maxWidth = data.maxWidth;

	return node;
};

DocMeasure.prototype.measureToc = function (node) {
	if (node.toc.title) {
		node.toc.title = this.measureNode(node.toc.title);
	}

	if (node.toc._items.length > 0) {
		var body = [];
		var textStyle = node.toc.textStyle || {};
		var numberStyle = node.toc.numberStyle || textStyle;
		var textMargin = node.toc.textMargin || [0, 0, 0, 0];
		for (var i = 0, l = node.toc._items.length; i < l; i++) {
			var item = node.toc._items[i];
			var lineStyle = item._textNodeRef.tocStyle || textStyle;
			var lineMargin = item._textNodeRef.tocMargin || textMargin;
			var lineNumberStyle = item._textNodeRef.tocNumberStyle || numberStyle;
			var destination = getNodeId(item._nodeRef);
			body.push([
				{ text: item._textNodeRef.text, linkToDestination: destination, alignment: 'left', style: lineStyle, margin: lineMargin },
				{ text: '00000', linkToDestination: destination, alignment: 'right', _tocItemRef: item._nodeRef, style: lineNumberStyle, margin: [0, lineMargin[1], 0, lineMargin[3]] }
			]);
		}


		node.toc._table = {
			table: {
				dontBreakRows: true,
				widths: ['*', 'auto'],
				body: body
			},
			layout: 'noBorders'
		};

		node.toc._table = this.measureNode(node.toc._table);
	}

	return node;
};

DocMeasure.prototype.measureVerticalContainer = function (node) {
	var items = node.stack;

	node._minWidth = 0;
	node._maxWidth = 0;

	for (var i = 0, l = items.length; i < l; i++) {
		items[i] = this.measureNode(items[i]);

		node._minWidth = Math.max(node._minWidth, items[i]._minWidth);
		node._maxWidth = Math.max(node._maxWidth, items[i]._maxWidth);
	}

	return node;
};

DocMeasure.prototype.gapSizeForList = function () {
	return this.textTools.sizeOfString('9. ', this.styleStack);
};

DocMeasure.prototype.buildUnorderedMarker = function (styleStack, gapSize, type) {
	function buildDisc(gapSize, color) {
		// TODO: ascender-based calculations
		var radius = gapSize.fontSize / 6;
		return {
			canvas: [{
				x: radius,
				y: (gapSize.height / gapSize.lineHeight) + gapSize.descender - gapSize.fontSize / 3,
				r1: radius,
				r2: radius,
				type: 'ellipse',
				color: color
			}]
		};
	}

	function buildSquare(gapSize, color) {
		// TODO: ascender-based calculations
		var size = gapSize.fontSize / 3;
		return {
			canvas: [{
				x: 0,
				y: (gapSize.height / gapSize.lineHeight) + gapSize.descender - (gapSize.fontSize / 3) - (size / 2),
				h: size,
				w: size,
				type: 'rect',
				color: color
			}]
		};
	}

	function buildCircle(gapSize, color) {
		// TODO: ascender-based calculations
		var radius = gapSize.fontSize / 6;
		return {
			canvas: [{
				x: radius,
				y: (gapSize.height / gapSize.lineHeight) + gapSize.descender - gapSize.fontSize / 3,
				r1: radius,
				r2: radius,
				type: 'ellipse',
				lineColor: color
			}]
		};
	}

	var marker;
	var color = styleStack.getProperty('markerColor') || styleStack.getProperty('color') || 'black';

	switch (type) {
		case 'circle':
			marker = buildCircle(gapSize, color);
			break;

		case 'square':
			marker = buildSquare(gapSize, color);
			break;

		case 'none':
			marker = {};
			break;

		case 'disc':
		default:
			marker = buildDisc(gapSize, color);
			break;
	}

	marker._minWidth = marker._maxWidth = gapSize.width;
	marker._minHeight = marker._maxHeight = gapSize.height;

	return marker;
};

DocMeasure.prototype.buildOrderedMarker = function (counter, styleStack, type, separator) {
	function prepareAlpha(counter) {
		function toAlpha(num) {
			return (num >= 26 ? toAlpha((num / 26 >> 0) - 1) : '') + 'abcdefghijklmnopqrstuvwxyz'[num % 26 >> 0];
		}

		if (counter < 1) {
			return counter.toString();
		}

		return toAlpha(counter - 1);
	}

	function prepareRoman(counter) {
		if (counter < 1 || counter > 4999) {
			return counter.toString();
		}
		var num = counter;
		var lookup = { M: 1000, CM: 900, D: 500, CD: 400, C: 100, XC: 90, L: 50, XL: 40, X: 10, IX: 9, V: 5, IV: 4, I: 1 }, roman = '', i;
		for (i in lookup) {
			while (num >= lookup[i]) {
				roman += i;
				num -= lookup[i];
			}
		}
		return roman;
	}

	function prepareDecimal(counter) {
		return counter.toString();
	}

	var counterText;
	switch (type) {
		case 'none':
			counterText = null;
			break;

		case 'upper-alpha':
			counterText = prepareAlpha(counter).toUpperCase();
			break;

		case 'lower-alpha':
			counterText = prepareAlpha(counter);
			break;

		case 'upper-roman':
			counterText = prepareRoman(counter);
			break;

		case 'lower-roman':
			counterText = prepareRoman(counter).toLowerCase();
			break;

		case 'decimal':
		default:
			counterText = prepareDecimal(counter);
			break;
	}

	if (counterText === null) {
		return {};
	}

	if (separator) {
		if (isArray(separator)) {
			if (separator[0]) {
				counterText = separator[0] + counterText;
			}

			if (separator[1]) {
				counterText += separator[1];
			}
			counterText += ' ';
		} else {
			counterText += separator + ' ';
		}
	}

	var textArray = { text: counterText };
	var markerColor = styleStack.getProperty('markerColor');
	if (markerColor) {
		textArray.color = markerColor;
	}

	return { _inlines: this.textTools.buildInlines(textArray, styleStack).items };
};

DocMeasure.prototype.measureUnorderedList = function (node) {
	var style = this.styleStack.clone();
	var items = node.ul;
	node.type = node.type || 'disc';
	node._gapSize = this.gapSizeForList();
	node._minWidth = 0;
	node._maxWidth = 0;

	for (var i = 0, l = items.length; i < l; i++) {
		var item = items[i] = this.measureNode(items[i]);

		if (!item.ol && !item.ul) {
			item.listMarker = this.buildUnorderedMarker(style, node._gapSize, item.listType || node.type);
		}

		node._minWidth = Math.max(node._minWidth, items[i]._minWidth + node._gapSize.width);
		node._maxWidth = Math.max(node._maxWidth, items[i]._maxWidth + node._gapSize.width);
	}

	return node;
};

DocMeasure.prototype.measureOrderedList = function (node) {
	var style = this.styleStack.clone();
	var items = node.ol;
	node.type = node.type || 'decimal';
	node.separator = node.separator || '.';
	node.reversed = node.reversed || false;
	if (!isNumber(node.start)) {
		node.start = node.reversed ? items.length : 1;
	}
	node._gapSize = this.gapSizeForList();
	node._minWidth = 0;
	node._maxWidth = 0;

	var counter = node.start;
	for (var i = 0, l = items.length; i < l; i++) {
		var item = items[i] = this.measureNode(items[i]);

		if (!item.ol && !item.ul) {
			var counterValue = isNumber(item.counter) ? item.counter : counter;
			item.listMarker = this.buildOrderedMarker(counterValue, style, item.listType || node.type, node.separator);
			if (item.listMarker._inlines) {
				node._gapSize.width = Math.max(node._gapSize.width, item.listMarker._inlines[0].width);
			}
		}  // TODO: else - nested lists numbering

		node._minWidth = Math.max(node._minWidth, items[i]._minWidth);
		node._maxWidth = Math.max(node._maxWidth, items[i]._maxWidth);

		if (node.reversed) {
			counter--;
		} else {
			counter++;
		}
	}

	node._minWidth += node._gapSize.width;
	node._maxWidth += node._gapSize.width;

	for (var i = 0, l = items.length; i < l; i++) {
		var item = items[i];
		if (!item.ol && !item.ul) {
			item.listMarker._minWidth = item.listMarker._maxWidth = node._gapSize.width;
		}
	}

	return node;
};

DocMeasure.prototype.measureColumns = function (node) {
	var columns = node.columns;
	node._gap = this.styleStack.getProperty('columnGap') || 0;

	for (var i = 0, l = columns.length; i < l; i++) {
		columns[i] = this.measureNode(columns[i]);
	}

	var measures = ColumnCalculator.measureMinMax(columns);

	var numGaps = (columns.length > 0) ? (columns.length - 1) : 0;
	node._minWidth = measures.min + node._gap * numGaps;
	node._maxWidth = measures.max + node._gap * numGaps;

	return node;
};

DocMeasure.prototype.measureTable = function (node) {
	extendTableWidths(node);
	node._layout = getLayout(this.tableLayouts);
	node._offsets = getOffsets(node._layout);

	var colSpans = [];
	var col, row, cols, rows;

	for (col = 0, cols = node.table.body[0].length; col < cols; col++) {
		var c = node.table.widths[col];
		c._minWidth = 0;
		c._maxWidth = 0;

		for (row = 0, rows = node.table.body.length; row < rows; row++) {
			var rowData = node.table.body[row];
			var data = rowData[col];
			if (data === undefined) {
				console.error('Malformed table row ', rowData, 'in node ', node);
				throw 'Malformed table row, a cell is undefined.';
			}
			if (data === null) { // transform to object
				data = '';
			}

			if (!data._span) {
				data = rowData[col] = this.styleStack.auto(data, measureCb(this, data));

				if (data.colSpan && data.colSpan > 1) {
					markSpans(rowData, col, data.colSpan);
					colSpans.push({ col: col, span: data.colSpan, minWidth: data._minWidth, maxWidth: data._maxWidth });
				} else {
					c._minWidth = Math.max(c._minWidth, data._minWidth);
					c._maxWidth = Math.max(c._maxWidth, data._maxWidth);
				}
			}

			if (data.rowSpan && data.rowSpan > 1) {
				markVSpans(node.table, row, col, data.rowSpan);
			}
		}
	}

	extendWidthsForColSpans();

	var measures = ColumnCalculator.measureMinMax(node.table.widths);

	node._minWidth = measures.min + node._offsets.total;
	node._maxWidth = measures.max + node._offsets.total;

	return node;

	function measureCb(_this, data) {
		return function () {
			if (isObject(data)) {
				data.fillColor = _this.styleStack.getProperty('fillColor');
				data.fillOpacity = _this.styleStack.getProperty('fillOpacity');
			}
			return _this.measureNode(data);
		};
	}

	function getLayout(tableLayouts) {
		var layout = node.layout;

		if (isString(layout)) {
			layout = tableLayouts[layout];
		}

		var defaultLayout = {
			hLineWidth: function (i, node) {
				return 1;
			},
			vLineWidth: function (i, node) {
				return 1;
			},
			hLineColor: function (i, node) {
				return 'black';
			},
			vLineColor: function (i, node) {
				return 'black';
			},
			hLineStyle: function (i, node) {
				return null;
			},
			vLineStyle: function (i, node) {
				return null;
			},
			paddingLeft: function (i, node) {
				return 4;
			},
			paddingRight: function (i, node) {
				return 4;
			},
			paddingTop: function (i, node) {
				return 2;
			},
			paddingBottom: function (i, node) {
				return 2;
			},
			fillColor: function (i, node) {
				return null;
			},
			fillOpacity: function (i, node) {
				return 1;
			},
			defaultBorder: true
		};

		return pack(defaultLayout, layout);
	}

	function getOffsets(layout) {
		var offsets = [];
		var totalOffset = 0;
		var prevRightPadding = 0;

		for (var i = 0, l = node.table.widths.length; i < l; i++) {
			var lOffset = prevRightPadding + layout.vLineWidth(i, node) + layout.paddingLeft(i, node);
			offsets.push(lOffset);
			totalOffset += lOffset;
			prevRightPadding = layout.paddingRight(i, node);
		}

		totalOffset += prevRightPadding + layout.vLineWidth(node.table.widths.length, node);

		return {
			total: totalOffset,
			offsets: offsets
		};
	}

	function extendWidthsForColSpans() {
		var q, j;

		for (var i = 0, l = colSpans.length; i < l; i++) {
			var span = colSpans[i];

			var currentMinMax = getMinMax(span.col, span.span, node._offsets);
			var minDifference = span.minWidth - currentMinMax.minWidth;
			var maxDifference = span.maxWidth - currentMinMax.maxWidth;

			if (minDifference > 0) {
				q = minDifference / span.span;

				for (j = 0; j < span.span; j++) {
					node.table.widths[span.col + j]._minWidth += q;
				}
			}

			if (maxDifference > 0) {
				q = maxDifference / span.span;

				for (j = 0; j < span.span; j++) {
					node.table.widths[span.col + j]._maxWidth += q;
				}
			}
		}
	}

	function getMinMax(col, span, offsets) {
		var result = { minWidth: 0, maxWidth: 0 };

		for (var i = 0; i < span; i++) {
			result.minWidth += node.table.widths[col + i]._minWidth + (i ? offsets.offsets[col + i] : 0);
			result.maxWidth += node.table.widths[col + i]._maxWidth + (i ? offsets.offsets[col + i] : 0);
		}

		return result;
	}

	function markSpans(rowData, col, span) {
		for (var i = 1; i < span; i++) {
			rowData[col + i] = {
				_span: true,
				_minWidth: 0,
				_maxWidth: 0,
				rowSpan: rowData[col].rowSpan
			};
		}
	}

	function markVSpans(table, row, col, span) {
		for (var i = 1; i < span; i++) {
			table.body[row + i][col] = {
				_span: true,
				_minWidth: 0,
				_maxWidth: 0,
				fillColor: table.body[row][col].fillColor,
				fillOpacity: table.body[row][col].fillOpacity
			};
		}
	}

	function extendTableWidths(node) {
		if (!node.table.widths) {
			node.table.widths = 'auto';
		}

		if (isString(node.table.widths)) {
			node.table.widths = [node.table.widths];

			while (node.table.widths.length < node.table.body[0].length) {
				node.table.widths.push(node.table.widths[node.table.widths.length - 1]);
			}
		}

		for (var i = 0, l = node.table.widths.length; i < l; i++) {
			var w = node.table.widths[i];
			if (isNumber(w) || isString(w)) {
				node.table.widths[i] = { width: w };
			}
		}
	}
};

DocMeasure.prototype.measureCanvas = function (node) {
	var w = 0, h = 0;

	for (var i = 0, l = node.canvas.length; i < l; i++) {
		var vector = node.canvas[i];

		switch (vector.type) {
			case 'ellipse':
				w = Math.max(w, vector.x + vector.r1);
				h = Math.max(h, vector.y + vector.r2);
				break;
			case 'rect':
				w = Math.max(w, vector.x + vector.w);
				h = Math.max(h, vector.y + vector.h);
				break;
			case 'line':
				w = Math.max(w, vector.x1, vector.x2);
				h = Math.max(h, vector.y1, vector.y2);
				break;
			case 'polyline':
				for (var i2 = 0, l2 = vector.points.length; i2 < l2; i2++) {
					w = Math.max(w, vector.points[i2].x);
					h = Math.max(h, vector.points[i2].y);
				}
				break;
		}
	}

	node._minWidth = node._maxWidth = w;
	node._minHeight = node._maxHeight = h;
	node._alignment = this.styleStack.getProperty('alignment');

	return node;
};

DocMeasure.prototype.measureQr = function (node) {
	node = qrEncoder.measure(node);
	node._alignment = this.styleStack.getProperty('alignment');
	return node;
};

module.exports = DocMeasure;


/***/ }),
/* 536 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/*eslint no-unused-vars: ["error", {"args": "none"}]*/
/*eslint no-redeclare: "off"*/


/* qr.js -- QR code generator in Javascript (revision 2011-01-19)
 * Written by Kang Seonghoon <public+qrjs@mearie.org>.
 *
 * This source code is in the public domain; if your jurisdiction does not
 * recognize the public domain the terms of Creative Commons CC0 license
 * apply. In the other words, you can always do what you want.
 */


// per-version information (cf. JIS X 0510:2004 pp. 30--36, 71)
//
// [0]: the degree of generator polynomial by ECC levels
// [1]: # of code blocks by ECC levels
// [2]: left-top positions of alignment patterns
//
// the number in this table (in particular, [0]) does not exactly match with
// the numbers in the specficiation. see augumenteccs below for the reason.
var VERSIONS = [
	null,
	[[10, 7, 17, 13], [1, 1, 1, 1], []],
	[[16, 10, 28, 22], [1, 1, 1, 1], [4, 16]],
	[[26, 15, 22, 18], [1, 1, 2, 2], [4, 20]],
	[[18, 20, 16, 26], [2, 1, 4, 2], [4, 24]],
	[[24, 26, 22, 18], [2, 1, 4, 4], [4, 28]],
	[[16, 18, 28, 24], [4, 2, 4, 4], [4, 32]],
	[[18, 20, 26, 18], [4, 2, 5, 6], [4, 20, 36]],
	[[22, 24, 26, 22], [4, 2, 6, 6], [4, 22, 40]],
	[[22, 30, 24, 20], [5, 2, 8, 8], [4, 24, 44]],
	[[26, 18, 28, 24], [5, 4, 8, 8], [4, 26, 48]],
	[[30, 20, 24, 28], [5, 4, 11, 8], [4, 28, 52]],
	[[22, 24, 28, 26], [8, 4, 11, 10], [4, 30, 56]],
	[[22, 26, 22, 24], [9, 4, 16, 12], [4, 32, 60]],
	[[24, 30, 24, 20], [9, 4, 16, 16], [4, 24, 44, 64]],
	[[24, 22, 24, 30], [10, 6, 18, 12], [4, 24, 46, 68]],
	[[28, 24, 30, 24], [10, 6, 16, 17], [4, 24, 48, 72]],
	[[28, 28, 28, 28], [11, 6, 19, 16], [4, 28, 52, 76]],
	[[26, 30, 28, 28], [13, 6, 21, 18], [4, 28, 54, 80]],
	[[26, 28, 26, 26], [14, 7, 25, 21], [4, 28, 56, 84]],
	[[26, 28, 28, 30], [16, 8, 25, 20], [4, 32, 60, 88]],
	[[26, 28, 30, 28], [17, 8, 25, 23], [4, 26, 48, 70, 92]],
	[[28, 28, 24, 30], [17, 9, 34, 23], [4, 24, 48, 72, 96]],
	[[28, 30, 30, 30], [18, 9, 30, 25], [4, 28, 52, 76, 100]],
	[[28, 30, 30, 30], [20, 10, 32, 27], [4, 26, 52, 78, 104]],
	[[28, 26, 30, 30], [21, 12, 35, 29], [4, 30, 56, 82, 108]],
	[[28, 28, 30, 28], [23, 12, 37, 34], [4, 28, 56, 84, 112]],
	[[28, 30, 30, 30], [25, 12, 40, 34], [4, 32, 60, 88, 116]],
	[[28, 30, 30, 30], [26, 13, 42, 35], [4, 24, 48, 72, 96, 120]],
	[[28, 30, 30, 30], [28, 14, 45, 38], [4, 28, 52, 76, 100, 124]],
	[[28, 30, 30, 30], [29, 15, 48, 40], [4, 24, 50, 76, 102, 128]],
	[[28, 30, 30, 30], [31, 16, 51, 43], [4, 28, 54, 80, 106, 132]],
	[[28, 30, 30, 30], [33, 17, 54, 45], [4, 32, 58, 84, 110, 136]],
	[[28, 30, 30, 30], [35, 18, 57, 48], [4, 28, 56, 84, 112, 140]],
	[[28, 30, 30, 30], [37, 19, 60, 51], [4, 32, 60, 88, 116, 144]],
	[[28, 30, 30, 30], [38, 19, 63, 53], [4, 28, 52, 76, 100, 124, 148]],
	[[28, 30, 30, 30], [40, 20, 66, 56], [4, 22, 48, 74, 100, 126, 152]],
	[[28, 30, 30, 30], [43, 21, 70, 59], [4, 26, 52, 78, 104, 130, 156]],
	[[28, 30, 30, 30], [45, 22, 74, 62], [4, 30, 56, 82, 108, 134, 160]],
	[[28, 30, 30, 30], [47, 24, 77, 65], [4, 24, 52, 80, 108, 136, 164]],
	[[28, 30, 30, 30], [49, 25, 81, 68], [4, 28, 56, 84, 112, 140, 168]]];

// mode constants (cf. Table 2 in JIS X 0510:2004 p. 16)
var MODE_TERMINATOR = 0;
var MODE_NUMERIC = 1, MODE_ALPHANUMERIC = 2, MODE_OCTET = 4, MODE_KANJI = 8;

// validation regexps
var NUMERIC_REGEXP = /^\d*$/;
var ALPHANUMERIC_REGEXP = /^[A-Za-z0-9 $%*+\-./:]*$/;
var ALPHANUMERIC_OUT_REGEXP = /^[A-Z0-9 $%*+\-./:]*$/;

// ECC levels (cf. Table 22 in JIS X 0510:2004 p. 45)
var ECCLEVEL_L = 1, ECCLEVEL_M = 0, ECCLEVEL_Q = 3, ECCLEVEL_H = 2;

// GF(2^8)-to-integer mapping with a reducing polynomial x^8+x^4+x^3+x^2+1
// invariant: GF256_MAP[GF256_INVMAP[i]] == i for all i in [1,256)
var GF256_MAP = [], GF256_INVMAP = [-1];
for (var i = 0, v = 1; i < 255; ++i) {
	GF256_MAP.push(v);
	GF256_INVMAP[v] = i;
	v = (v * 2) ^ (v >= 128 ? 0x11d : 0);
}

// generator polynomials up to degree 30
// (should match with polynomials in JIS X 0510:2004 Appendix A)
//
// generator polynomial of degree K is product of (x-\alpha^0), (x-\alpha^1),
// ..., (x-\alpha^(K-1)). by convention, we omit the K-th coefficient (always 1)
// from the result; also other coefficients are written in terms of the exponent
// to \alpha to avoid the redundant calculation. (see also calculateecc below.)
var GF256_GENPOLY = [[]];
for (var i = 0; i < 30; ++i) {
	var prevpoly = GF256_GENPOLY[i], poly = [];
	for (var j = 0; j <= i; ++j) {
		var a = (j < i ? GF256_MAP[prevpoly[j]] : 0);
		var b = GF256_MAP[(i + (prevpoly[j - 1] || 0)) % 255];
		poly.push(GF256_INVMAP[a ^ b]);
	}
	GF256_GENPOLY.push(poly);
}

// alphanumeric character mapping (cf. Table 5 in JIS X 0510:2004 p. 19)
var ALPHANUMERIC_MAP = {};
for (var i = 0; i < 45; ++i) {
	ALPHANUMERIC_MAP['0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:'.charAt(i)] = i;
}

// mask functions in terms of row # and column #
// (cf. Table 20 in JIS X 0510:2004 p. 42)
/*jshint unused: false */
var MASKFUNCS = [
	function (i, j) {
		return (i + j) % 2 === 0;
	},
	function (i, j) {
		return i % 2 === 0;
	},
	function (i, j) {
		return j % 3 === 0;
	},
	function (i, j) {
		return (i + j) % 3 === 0;
	},
	function (i, j) {
		return (((i / 2) | 0) + ((j / 3) | 0)) % 2 === 0;
	},
	function (i, j) {
		return (i * j) % 2 + (i * j) % 3 === 0;
	},
	function (i, j) {
		return ((i * j) % 2 + (i * j) % 3) % 2 === 0;
	},
	function (i, j) {
		return ((i + j) % 2 + (i * j) % 3) % 2 === 0;
	}];

// returns true when the version information has to be embeded.
var needsverinfo = function (ver) {
	return ver > 6;
};

// returns the size of entire QR code for given version.
var getsizebyver = function (ver) {
	return 4 * ver + 17;
};

// returns the number of bits available for code words in this version.
var nfullbits = function (ver) {
	/*
	 * |<--------------- n --------------->|
	 * |        |<----- n-17 ---->|        |
	 * +-------+                ///+-------+ ----
	 * |       |                ///|       |    ^
	 * |  9x9  |       @@@@@    ///|  9x8  |    |
	 * |       | # # # @5x5@ # # # |       |    |
	 * +-------+       @@@@@       +-------+    |
	 *       #                               ---|
	 *                                        ^ |
	 *       #                                |
	 *     @@@@@       @@@@@       @@@@@      | n
	 *     @5x5@       @5x5@       @5x5@   n-17
	 *     @@@@@       @@@@@       @@@@@      | |
	 *       #                                | |
	 * //////                                 v |
	 * //////#                               ---|
	 * +-------+       @@@@@       @@@@@        |
	 * |       |       @5x5@       @5x5@        |
	 * |  8x9  |       @@@@@       @@@@@        |
	 * |       |                                v
	 * +-------+                             ----
	 *
	 * when the entire code has n^2 modules and there are m^2-3 alignment
	 * patterns, we have:
	 * - 225 (= 9x9 + 9x8 + 8x9) modules for finder patterns and
	 *   format information;
	 * - 2n-34 (= 2(n-17)) modules for timing patterns;
	 * - 36 (= 3x6 + 6x3) modules for version information, if any;
	 * - 25m^2-75 (= (m^2-3)(5x5)) modules for alignment patterns
	 *   if any, but 10m-20 (= 2(m-2)x5) of them overlaps with
	 *   timing patterns.
	 */
	var v = VERSIONS[ver];
	var nbits = 16 * ver * ver + 128 * ver + 64; // finder, timing and format info.
	if (needsverinfo(ver))
		nbits -= 36; // version information
	if (v[2].length) { // alignment patterns
		nbits -= 25 * v[2].length * v[2].length - 10 * v[2].length - 55;
	}
	return nbits;
};

// returns the number of bits available for data portions (i.e. excludes ECC
// bits but includes mode and length bits) in this version and ECC level.
var ndatabits = function (ver, ecclevel) {
	var nbits = nfullbits(ver) & ~7; // no sub-octet code words
	var v = VERSIONS[ver];
	nbits -= 8 * v[0][ecclevel] * v[1][ecclevel]; // ecc bits
	return nbits;
};

// returns the number of bits required for the length of data.
// (cf. Table 3 in JIS X 0510:2004 p. 16)
var ndatalenbits = function (ver, mode) {
	switch (mode) {
		case MODE_NUMERIC:
			return (ver < 10 ? 10 : ver < 27 ? 12 : 14);
		case MODE_ALPHANUMERIC:
			return (ver < 10 ? 9 : ver < 27 ? 11 : 13);
		case MODE_OCTET:
			return (ver < 10 ? 8 : 16);
		case MODE_KANJI:
			return (ver < 10 ? 8 : ver < 27 ? 10 : 12);
	}
};

// returns the maximum length of data possible in given configuration.
var getmaxdatalen = function (ver, mode, ecclevel) {
	var nbits = ndatabits(ver, ecclevel) - 4 - ndatalenbits(ver, mode); // 4 for mode bits
	switch (mode) {
		case MODE_NUMERIC:
			return ((nbits / 10) | 0) * 3 + (nbits % 10 < 4 ? 0 : nbits % 10 < 7 ? 1 : 2);
		case MODE_ALPHANUMERIC:
			return ((nbits / 11) | 0) * 2 + (nbits % 11 < 6 ? 0 : 1);
		case MODE_OCTET:
			return (nbits / 8) | 0;
		case MODE_KANJI:
			return (nbits / 13) | 0;
	}
};

// checks if the given data can be encoded in given mode, and returns
// the converted data for the further processing if possible. otherwise
// returns null.
//
// this function does not check the length of data; it is a duty of
// encode function below (as it depends on the version and ECC level too).
var validatedata = function (mode, data) {
	switch (mode) {
		case MODE_NUMERIC:
			if (!data.match(NUMERIC_REGEXP))
				return null;
			return data;

		case MODE_ALPHANUMERIC:
			if (!data.match(ALPHANUMERIC_REGEXP))
				return null;
			return data.toUpperCase();

		case MODE_OCTET:
			if (typeof data === 'string') { // encode as utf-8 string
				var newdata = [];
				for (var i = 0; i < data.length; ++i) {
					var ch = data.charCodeAt(i);
					if (ch < 0x80) {
						newdata.push(ch);
					} else if (ch < 0x800) {
						newdata.push(0xc0 | (ch >> 6),
							0x80 | (ch & 0x3f));
					} else if (ch < 0x10000) {
						newdata.push(0xe0 | (ch >> 12),
							0x80 | ((ch >> 6) & 0x3f),
							0x80 | (ch & 0x3f));
					} else {
						newdata.push(0xf0 | (ch >> 18),
							0x80 | ((ch >> 12) & 0x3f),
							0x80 | ((ch >> 6) & 0x3f),
							0x80 | (ch & 0x3f));
					}
				}
				return newdata;
			} else {
				return data;
			}
	}
};

// returns the code words (sans ECC bits) for given data and configurations.
// requires data to be preprocessed by validatedata. no length check is
// performed, and everything has to be checked before calling this function.
var encode = function (ver, mode, data, maxbuflen) {
	var buf = [];
	var bits = 0, remaining = 8;
	var datalen = data.length;

	// this function is intentionally no-op when n=0.
	var pack = function (x, n) {
		if (n >= remaining) {
			buf.push(bits | (x >> (n -= remaining)));
			while (n >= 8)
				buf.push((x >> (n -= 8)) & 255);
			bits = 0;
			remaining = 8;
		}
		if (n > 0)
			bits |= (x & ((1 << n) - 1)) << (remaining -= n);
	};

	var nlenbits = ndatalenbits(ver, mode);
	pack(mode, 4);
	pack(datalen, nlenbits);

	switch (mode) {
		case MODE_NUMERIC:
			for (var i = 2; i < datalen; i += 3) {
				pack(parseInt(data.substring(i - 2, i + 1), 10), 10);
			}
			pack(parseInt(data.substring(i - 2), 10), [0, 4, 7][datalen % 3]);
			break;

		case MODE_ALPHANUMERIC:
			for (var i = 1; i < datalen; i += 2) {
				pack(ALPHANUMERIC_MAP[data.charAt(i - 1)] * 45 +
					ALPHANUMERIC_MAP[data.charAt(i)], 11);
			}
			if (datalen % 2 == 1) {
				pack(ALPHANUMERIC_MAP[data.charAt(i - 1)], 6);
			}
			break;

		case MODE_OCTET:
			for (var i = 0; i < datalen; ++i) {
				pack(data[i], 8);
			}
			break;
	}

	// final bits. it is possible that adding terminator causes the buffer
	// to overflow, but then the buffer truncated to the maximum size will
	// be valid as the truncated terminator mode bits and padding is
	// identical in appearance (cf. JIS X 0510:2004 sec 8.4.8).
	pack(MODE_TERMINATOR, 4);
	if (remaining < 8)
		buf.push(bits);

	// the padding to fill up the remaining space. we should not add any
	// words when the overflow already occurred.
	while (buf.length + 1 < maxbuflen)
		buf.push(0xec, 0x11);
	if (buf.length < maxbuflen)
		buf.push(0xec);
	return buf;
};

// calculates ECC code words for given code words and generator polynomial.
//
// this is quite similar to CRC calculation as both Reed-Solomon and CRC use
// the certain kind of cyclic codes, which is effectively the division of
// zero-augumented polynomial by the generator polynomial. the only difference
// is that Reed-Solomon uses GF(2^8), instead of CRC's GF(2), and Reed-Solomon
// uses the different generator polynomial than CRC's.
var calculateecc = function (poly, genpoly) {
	var modulus = poly.slice(0);
	var polylen = poly.length, genpolylen = genpoly.length;
	for (var i = 0; i < genpolylen; ++i)
		modulus.push(0);
	for (var i = 0; i < polylen; ) {
		var quotient = GF256_INVMAP[modulus[i++]];
		if (quotient >= 0) {
			for (var j = 0; j < genpolylen; ++j) {
				modulus[i + j] ^= GF256_MAP[(quotient + genpoly[j]) % 255];
			}
		}
	}
	return modulus.slice(polylen);
};

// auguments ECC code words to given code words. the resulting words are
// ready to be encoded in the matrix.
//
// the much of actual augumenting procedure follows JIS X 0510:2004 sec 8.7.
// the code is simplified using the fact that the size of each code & ECC
// blocks is almost same; for example, when we have 4 blocks and 46 data words
// the number of code words in those blocks are 11, 11, 12, 12 respectively.
var augumenteccs = function (poly, nblocks, genpoly) {
	var subsizes = [];
	var subsize = (poly.length / nblocks) | 0, subsize0 = 0;
	var pivot = nblocks - poly.length % nblocks;
	for (var i = 0; i < pivot; ++i) {
		subsizes.push(subsize0);
		subsize0 += subsize;
	}
	for (var i = pivot; i < nblocks; ++i) {
		subsizes.push(subsize0);
		subsize0 += subsize + 1;
	}
	subsizes.push(subsize0);

	var eccs = [];
	for (var i = 0; i < nblocks; ++i) {
		eccs.push(calculateecc(poly.slice(subsizes[i], subsizes[i + 1]), genpoly));
	}

	var result = [];
	var nitemsperblock = (poly.length / nblocks) | 0;
	for (var i = 0; i < nitemsperblock; ++i) {
		for (var j = 0; j < nblocks; ++j) {
			result.push(poly[subsizes[j] + i]);
		}
	}
	for (var j = pivot; j < nblocks; ++j) {
		result.push(poly[subsizes[j + 1] - 1]);
	}
	for (var i = 0; i < genpoly.length; ++i) {
		for (var j = 0; j < nblocks; ++j) {
			result.push(eccs[j][i]);
		}
	}
	return result;
};

// auguments BCH(p+q,q) code to the polynomial over GF(2), given the proper
// genpoly. the both input and output are in binary numbers, and unlike
// calculateecc genpoly should include the 1 bit for the highest degree.
//
// actual polynomials used for this procedure are as follows:
// - p=10, q=5, genpoly=x^10+x^8+x^5+x^4+x^2+x+1 (JIS X 0510:2004 Appendix C)
// - p=18, q=6, genpoly=x^12+x^11+x^10+x^9+x^8+x^5+x^2+1 (ibid. Appendix D)
var augumentbch = function (poly, p, genpoly, q) {
	var modulus = poly << q;
	for (var i = p - 1; i >= 0; --i) {
		if ((modulus >> (q + i)) & 1)
			modulus ^= genpoly << i;
	}
	return (poly << q) | modulus;
};

// creates the base matrix for given version. it returns two matrices, one of
// them is the actual one and the another represents the "reserved" portion
// (e.g. finder and timing patterns) of the matrix.
//
// some entries in the matrix may be undefined, rather than 0 or 1. this is
// intentional (no initialization needed!), and putdata below will fill
// the remaining ones.
var makebasematrix = function (ver) {
	var v = VERSIONS[ver], n = getsizebyver(ver);
	var matrix = [], reserved = [];
	for (var i = 0; i < n; ++i) {
		matrix.push([]);
		reserved.push([]);
	}

	var blit = function (y, x, h, w, bits) {
		for (var i = 0; i < h; ++i) {
			for (var j = 0; j < w; ++j) {
				matrix[y + i][x + j] = (bits[i] >> j) & 1;
				reserved[y + i][x + j] = 1;
			}
		}
	};

	// finder patterns and a part of timing patterns
	// will also mark the format information area (not yet written) as reserved.
	blit(0, 0, 9, 9, [0x7f, 0x41, 0x5d, 0x5d, 0x5d, 0x41, 0x17f, 0x00, 0x40]);
	blit(n - 8, 0, 8, 9, [0x100, 0x7f, 0x41, 0x5d, 0x5d, 0x5d, 0x41, 0x7f]);
	blit(0, n - 8, 9, 8, [0xfe, 0x82, 0xba, 0xba, 0xba, 0x82, 0xfe, 0x00, 0x00]);

	// the rest of timing patterns
	for (var i = 9; i < n - 8; ++i) {
		matrix[6][i] = matrix[i][6] = ~i & 1;
		reserved[6][i] = reserved[i][6] = 1;
	}

	// alignment patterns
	var aligns = v[2], m = aligns.length;
	for (var i = 0; i < m; ++i) {
		var minj = (i === 0 || i === m - 1 ? 1 : 0), maxj = (i === 0 ? m - 1 : m);
		for (var j = minj; j < maxj; ++j) {
			blit(aligns[i], aligns[j], 5, 5, [0x1f, 0x11, 0x15, 0x11, 0x1f]);
		}
	}

	// version information
	if (needsverinfo(ver)) {
		var code = augumentbch(ver, 6, 0x1f25, 12);
		var k = 0;
		for (var i = 0; i < 6; ++i) {
			for (var j = 0; j < 3; ++j) {
				matrix[i][(n - 11) + j] = matrix[(n - 11) + j][i] = (code >> k++) & 1;
				reserved[i][(n - 11) + j] = reserved[(n - 11) + j][i] = 1;
			}
		}
	}

	return {matrix: matrix, reserved: reserved};
};

// fills the data portion (i.e. unmarked in reserved) of the matrix with given
// code words. the size of code words should be no more than available bits,
// and remaining bits are padded to 0 (cf. JIS X 0510:2004 sec 8.7.3).
var putdata = function (matrix, reserved, buf) {
	var n = matrix.length;
	var k = 0, dir = -1;
	for (var i = n - 1; i >= 0; i -= 2) {
		if (i == 6)
			--i; // skip the entire timing pattern column
		var jj = (dir < 0 ? n - 1 : 0);
		for (var j = 0; j < n; ++j) {
			for (var ii = i; ii > i - 2; --ii) {
				if (!reserved[jj][ii]) {
					// may overflow, but (undefined >> x)
					// is 0 so it will auto-pad to zero.
					matrix[jj][ii] = (buf[k >> 3] >> (~k & 7)) & 1;
					++k;
				}
			}
			jj += dir;
		}
		dir = -dir;
	}
	return matrix;
};

// XOR-masks the data portion of the matrix. repeating the call with the same
// arguments will revert the prior call (convenient in the matrix evaluation).
var maskdata = function (matrix, reserved, mask) {
	var maskf = MASKFUNCS[mask];
	var n = matrix.length;
	for (var i = 0; i < n; ++i) {
		for (var j = 0; j < n; ++j) {
			if (!reserved[i][j])
				matrix[i][j] ^= maskf(i, j);
		}
	}
	return matrix;
};

// puts the format information.
var putformatinfo = function (matrix, reserved, ecclevel, mask) {
	var n = matrix.length;
	var code = augumentbch((ecclevel << 3) | mask, 5, 0x537, 10) ^ 0x5412;
	for (var i = 0; i < 15; ++i) {
		var r = [0, 1, 2, 3, 4, 5, 7, 8, n - 7, n - 6, n - 5, n - 4, n - 3, n - 2, n - 1][i];
		var c = [n - 1, n - 2, n - 3, n - 4, n - 5, n - 6, n - 7, n - 8, 7, 5, 4, 3, 2, 1, 0][i];
		matrix[r][8] = matrix[8][c] = (code >> i) & 1;
		// we don't have to mark those bits reserved; always done
		// in makebasematrix above.
	}
	return matrix;
};

// evaluates the resulting matrix and returns the score (lower is better).
// (cf. JIS X 0510:2004 sec 8.8.2)
//
// the evaluation procedure tries to avoid the problematic patterns naturally
// occuring from the original matrix. for example, it penaltizes the patterns
// which just look like the finder pattern which will confuse the decoder.
// we choose the mask which results in the lowest score among 8 possible ones.
//
// note: zxing seems to use the same procedure and in many cases its choice
// agrees to ours, but sometimes it does not. practically it doesn't matter.
var evaluatematrix = function (matrix) {
	// N1+(k-5) points for each consecutive row of k same-colored modules,
	// where k >= 5. no overlapping row counts.
	var PENALTY_CONSECUTIVE = 3;
	// N2 points for each 2x2 block of same-colored modules.
	// overlapping block does count.
	var PENALTY_TWOBYTWO = 3;
	// N3 points for each pattern with >4W:1B:1W:3B:1W:1B or
	// 1B:1W:3B:1W:1B:>4W, or their multiples (e.g. highly unlikely,
	// but 13W:3B:3W:9B:3W:3B counts).
	var PENALTY_FINDERLIKE = 40;
	// N4*k points for every (5*k)% deviation from 50% black density.
	// i.e. k=1 for 55~60% and 40~45%, k=2 for 60~65% and 35~40%, etc.
	var PENALTY_DENSITY = 10;

	var evaluategroup = function (groups) { // assumes [W,B,W,B,W,...,B,W]
		var score = 0;
		for (var i = 0; i < groups.length; ++i) {
			if (groups[i] >= 5)
				score += PENALTY_CONSECUTIVE + (groups[i] - 5);
		}
		for (var i = 5; i < groups.length; i += 2) {
			var p = groups[i];
			if (groups[i - 1] == p && groups[i - 2] == 3 * p && groups[i - 3] == p &&
				groups[i - 4] == p && (groups[i - 5] >= 4 * p || groups[i + 1] >= 4 * p)) {
				// this part differs from zxing...
				score += PENALTY_FINDERLIKE;
			}
		}
		return score;
	};

	var n = matrix.length;
	var score = 0, nblacks = 0;
	for (var i = 0; i < n; ++i) {
		var row = matrix[i];
		var groups;

		// evaluate the current row
		groups = [0]; // the first empty group of white
		for (var j = 0; j < n; ) {
			var k;
			for (k = 0; j < n && row[j]; ++k)
				++j;
			groups.push(k);
			for (k = 0; j < n && !row[j]; ++k)
				++j;
			groups.push(k);
		}
		score += evaluategroup(groups);

		// evaluate the current column
		groups = [0];
		for (var j = 0; j < n; ) {
			var k;
			for (k = 0; j < n && matrix[j][i]; ++k)
				++j;
			groups.push(k);
			for (k = 0; j < n && !matrix[j][i]; ++k)
				++j;
			groups.push(k);
		}
		score += evaluategroup(groups);

		// check the 2x2 box and calculate the density
		var nextrow = matrix[i + 1] || [];
		nblacks += row[0];
		for (var j = 1; j < n; ++j) {
			var p = row[j];
			nblacks += p;
			// at least comparison with next row should be strict...
			if (row[j - 1] == p && nextrow[j] === p && nextrow[j - 1] === p) {
				score += PENALTY_TWOBYTWO;
			}
		}
	}

	score += PENALTY_DENSITY * ((Math.abs(nblacks / n / n - 0.5) / 0.05) | 0);
	return score;
};

// returns the fully encoded QR code matrix which contains given data.
// it also chooses the best mask automatically when mask is -1.
var generate = function (data, ver, mode, ecclevel, mask) {
	var v = VERSIONS[ver];
	var buf = encode(ver, mode, data, ndatabits(ver, ecclevel) >> 3);
	buf = augumenteccs(buf, v[1][ecclevel], GF256_GENPOLY[v[0][ecclevel]]);

	var result = makebasematrix(ver);
	var matrix = result.matrix, reserved = result.reserved;
	putdata(matrix, reserved, buf);

	if (mask < 0) {
		// find the best mask
		maskdata(matrix, reserved, 0);
		putformatinfo(matrix, reserved, ecclevel, 0);
		var bestmask = 0, bestscore = evaluatematrix(matrix);
		maskdata(matrix, reserved, 0);
		for (mask = 1; mask < 8; ++mask) {
			maskdata(matrix, reserved, mask);
			putformatinfo(matrix, reserved, ecclevel, mask);
			var score = evaluatematrix(matrix);
			if (bestscore > score) {
				bestscore = score;
				bestmask = mask;
			}
			maskdata(matrix, reserved, mask);
		}
		mask = bestmask;
	}

	maskdata(matrix, reserved, mask);
	putformatinfo(matrix, reserved, ecclevel, mask);
	return matrix;
};

// the public interface is trivial; the options available are as follows:
//
// - version: an integer in [1,40]. when omitted (or -1) the smallest possible
//   version is chosen.
// - mode: one of 'numeric', 'alphanumeric', 'octet'. when omitted the smallest
//   possible mode is chosen.
// - eccLevel: one of 'L', 'M', 'Q', 'H'. defaults to 'L'.
// - mask: an integer in [0,7]. when omitted (or -1) the best mask is chosen.
//

function generateFrame(data, options) {
	var MODES = {'numeric': MODE_NUMERIC, 'alphanumeric': MODE_ALPHANUMERIC,
		'octet': MODE_OCTET};
	var ECCLEVELS = {'L': ECCLEVEL_L, 'M': ECCLEVEL_M, 'Q': ECCLEVEL_Q,
		'H': ECCLEVEL_H};

	options = options || {};
	var ver = options.version || -1;
	var ecclevel = ECCLEVELS[(options.eccLevel || 'L').toUpperCase()];
	var mode = options.mode ? MODES[options.mode.toLowerCase()] : -1;
	var mask = 'mask' in options ? options.mask : -1;

	if (mode < 0) {
		if (typeof data === 'string') {
			if (data.match(NUMERIC_REGEXP)) {
				mode = MODE_NUMERIC;
			} else if (data.match(ALPHANUMERIC_OUT_REGEXP)) {
				// while encode supports case-insensitive encoding, we restrict the data to be uppercased when auto-selecting the mode.
				mode = MODE_ALPHANUMERIC;
			} else {
				mode = MODE_OCTET;
			}
		} else {
			mode = MODE_OCTET;
		}
	} else if (!(mode == MODE_NUMERIC || mode == MODE_ALPHANUMERIC ||
		mode == MODE_OCTET)) {
		throw 'invalid or unsupported mode';
	}

	data = validatedata(mode, data);
	if (data === null)
		throw 'invalid data format';

	if (ecclevel < 0 || ecclevel > 3)
		throw 'invalid ECC level';

	if (ver < 0) {
		for (ver = 1; ver <= 40; ++ver) {
			if (data.length <= getmaxdatalen(ver, mode, ecclevel))
				break;
		}
		if (ver > 40)
			throw 'too large data for the Qr format';
	} else if (ver < 1 || ver > 40) {
		throw 'invalid Qr version! should be between 1 and 40';
	}

	if (mask != -1 && (mask < 0 || mask > 8))
		throw 'invalid mask';
	//console.log('version:', ver, 'mode:', mode, 'ECC:', ecclevel, 'mask:', mask )
	return generate(data, ver, mode, ecclevel, mask);
}


// options
// - modulesize: a number. this is a size of each modules in pixels, and
//   defaults to 5px.
// - margin: a number. this is a size of margin in *modules*, and defaults to
//   4 (white modules). the specficiation mandates the margin no less than 4
//   modules, so it is better not to alter this value unless you know what
//   you're doing.
function buildCanvas(data, options) {

	var canvas = [];
	var background = options.background || '#fff';
	var foreground = options.foreground || '#000';
	//var margin = options.margin || 4;
	var matrix = generateFrame(data, options);
	var n = matrix.length;
	var modSize = Math.floor(options.fit ? options.fit / n : 5);
	var size = n * modSize;

	canvas.push({
		type: 'rect',
		x: 0, y: 0, w: size, h: size, lineWidth: 0, color: background
	});

	for (var i = 0; i < n; ++i) {
		for (var j = 0; j < n; ++j) {
			if (matrix[i][j]) {
				canvas.push({
					type: 'rect',
					x: modSize * j,
					y: modSize * i,
					w: modSize,
					h: modSize,
					lineWidth: 0,
					color: foreground
				});
			}
		}
	}

	return {
		canvas: canvas,
		size: size
	};

}

function measure(node) {
	var cd = buildCanvas(node.qr, node);
	node._canvas = cd.canvas;
	node._width = node._height = node._minWidth = node._maxWidth = node._minHeight = node._maxHeight = cd.size;
	return node;
}

module.exports = {
	measure: measure
};

/***/ }),
/* 537 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var isUndefined = __webpack_require__(0).isUndefined;
var ElementWriter = __webpack_require__(538);

/**
 * Creates an instance of PageElementWriter - an extended ElementWriter
 * which can handle:
 * - page-breaks (it adds new pages when there's not enough space left),
 * - repeatable fragments (like table-headers, which are repeated everytime
 *                         a page-break occurs)
 * - transactions (used for unbreakable-blocks when we want to make sure
 *                 whole block will be rendered on the same page)
 */
function PageElementWriter(context, tracker) {
	this.transactionLevel = 0;
	this.repeatables = [];
	this.tracker = tracker;
	this.writer = new ElementWriter(context, tracker);
}

function fitOnPage(self, addFct) {
	var position = addFct(self);
	if (!position) {
		self.moveToNextPage();
		position = addFct(self);
	}
	return position;
}

PageElementWriter.prototype.addLine = function (line, dontUpdateContextPosition, index) {
	return fitOnPage(this, function (self) {
		return self.writer.addLine(line, dontUpdateContextPosition, index);
	});
};

PageElementWriter.prototype.addImage = function (image, index) {
	return fitOnPage(this, function (self) {
		return self.writer.addImage(image, index);
	});
};

PageElementWriter.prototype.addSVG = function (image, index) {
	return fitOnPage(this, function (self) {
		return self.writer.addSVG(image, index);
	});
};

PageElementWriter.prototype.addQr = function (qr, index) {
	return fitOnPage(this, function (self) {
		return self.writer.addQr(qr, index);
	});
};

PageElementWriter.prototype.addVector = function (vector, ignoreContextX, ignoreContextY, index) {
	return this.writer.addVector(vector, ignoreContextX, ignoreContextY, index);
};

PageElementWriter.prototype.beginClip = function (width, height) {
	return this.writer.beginClip(width, height);
};

PageElementWriter.prototype.endClip = function () {
	return this.writer.endClip();
};

PageElementWriter.prototype.alignCanvas = function (node) {
	this.writer.alignCanvas(node);
};

PageElementWriter.prototype.addFragment = function (fragment, useBlockXOffset, useBlockYOffset, dontUpdateContextPosition) {
	if (!this.writer.addFragment(fragment, useBlockXOffset, useBlockYOffset, dontUpdateContextPosition)) {
		this.moveToNextPage();
		this.writer.addFragment(fragment, useBlockXOffset, useBlockYOffset, dontUpdateContextPosition);
	}
};

PageElementWriter.prototype.moveToNextPage = function (pageOrientation) {

	var nextPage = this.writer.context.moveToNextPage(pageOrientation);

	// moveToNextPage is called multiple times for table, because is called for each column
	// and repeatables are inserted only in the first time. If columns are used, is needed
	// call for table in first column and then for table in the second column (is other repeatables).
	this.repeatables.forEach(function (rep) {
		if (isUndefined(rep.insertedOnPages[this.writer.context.page])) {
			rep.insertedOnPages[this.writer.context.page] = true;
			this.writer.addFragment(rep, true);
		} else {
			this.writer.context.moveDown(rep.height);
		}
	}, this);

	this.writer.tracker.emit('pageChanged', {
		prevPage: nextPage.prevPage,
		prevY: nextPage.prevY,
		y: this.writer.context.y
	});
};

PageElementWriter.prototype.beginUnbreakableBlock = function (width, height) {
	if (this.transactionLevel++ === 0) {
		this.originalX = this.writer.context.x;
		this.writer.pushContext(width, height);
	}
};

PageElementWriter.prototype.commitUnbreakableBlock = function (forcedX, forcedY) {
	if (--this.transactionLevel === 0) {
		var unbreakableContext = this.writer.context;
		this.writer.popContext();

		var nbPages = unbreakableContext.pages.length;
		if (nbPages > 0) {
			// no support for multi-page unbreakableBlocks
			var fragment = unbreakableContext.pages[0];
			fragment.xOffset = forcedX;
			fragment.yOffset = forcedY;

			//TODO: vectors can influence height in some situations
			if (nbPages > 1) {
				// on out-of-context blocs (headers, footers, background) height should be the whole DocumentContext height
				if (forcedX !== undefined || forcedY !== undefined) {
					fragment.height = unbreakableContext.getCurrentPage().pageSize.height - unbreakableContext.pageMargins.top - unbreakableContext.pageMargins.bottom;
				} else {
					fragment.height = this.writer.context.getCurrentPage().pageSize.height - this.writer.context.pageMargins.top - this.writer.context.pageMargins.bottom;
					for (var i = 0, l = this.repeatables.length; i < l; i++) {
						fragment.height -= this.repeatables[i].height;
					}
				}
			} else {
				fragment.height = unbreakableContext.y;
			}

			if (forcedX !== undefined || forcedY !== undefined) {
				this.writer.addFragment(fragment, true, true, true);
			} else {
				this.addFragment(fragment);
			}
		}
	}
};

PageElementWriter.prototype.currentBlockToRepeatable = function () {
	var unbreakableContext = this.writer.context;
	var rep = { items: [] };

	unbreakableContext.pages[0].items.forEach(function (item) {
		rep.items.push(item);
	});

	rep.xOffset = this.originalX;

	//TODO: vectors can influence height in some situations
	rep.height = unbreakableContext.y;

	rep.insertedOnPages = [];

	return rep;
};

PageElementWriter.prototype.pushToRepeatables = function (rep) {
	this.repeatables.push(rep);
};

PageElementWriter.prototype.popFromRepeatables = function () {
	this.repeatables.pop();
};

PageElementWriter.prototype.context = function () {
	return this.writer.context;
};

module.exports = PageElementWriter;


/***/ }),
/* 538 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var Line = __webpack_require__(294);
var isNumber = __webpack_require__(0).isNumber;
var pack = __webpack_require__(0).pack;
var offsetVector = __webpack_require__(0).offsetVector;
var DocumentContext = __webpack_require__(293);

/**
 * Creates an instance of ElementWriter - a line/vector writer, which adds
 * elements to current page and sets their positions based on the context
 */
function ElementWriter(context, tracker) {
	this.context = context;
	this.contextStack = [];
	this.tracker = tracker;
}

function addPageItem(page, item, index) {
	if (index === null || index === undefined || index < 0 || index > page.items.length) {
		page.items.push(item);
	} else {
		page.items.splice(index, 0, item);
	}
}

ElementWriter.prototype.addLine = function (line, dontUpdateContextPosition, index) {
	var height = line.getHeight();
	var context = this.context;
	var page = context.getCurrentPage(),
		position = this.getCurrentPositionOnPage();

	if (context.availableHeight < height || !page) {
		return false;
	}

	line.x = context.x + (line.x || 0);
	line.y = context.y + (line.y || 0);

	this.alignLine(line);

	addPageItem(page, {
		type: 'line',
		item: line
	}, index);
	this.tracker.emit('lineAdded', line);

	if (!dontUpdateContextPosition) {
		context.moveDown(height);
	}

	return position;
};

ElementWriter.prototype.alignLine = function (line) {
	var width = this.context.availableWidth;
	var lineWidth = line.getWidth();

	var alignment = line.inlines && line.inlines.length > 0 && line.inlines[0].alignment;

	var offset = 0;
	switch (alignment) {
		case 'right':
			offset = width - lineWidth;
			break;
		case 'center':
			offset = (width - lineWidth) / 2;
			break;
	}

	if (offset) {
		line.x = (line.x || 0) + offset;
	}

	if (alignment === 'justify' &&
		!line.newLineForced &&
		!line.lastLineInParagraph &&
		line.inlines.length > 1) {
		var additionalSpacing = (width - lineWidth) / (line.inlines.length - 1);

		for (var i = 1, l = line.inlines.length; i < l; i++) {
			offset = i * additionalSpacing;

			line.inlines[i].x += offset;
			line.inlines[i].justifyShift = additionalSpacing;
		}
	}
};

ElementWriter.prototype.addImage = function (image, index, type) {
	var context = this.context;
	var page = context.getCurrentPage(),
		position = this.getCurrentPositionOnPage();

	if (!page || (image.absolutePosition === undefined && context.availableHeight < image._height && page.items.length > 0)) {
		return false;
	}

	if (image._x === undefined) {
		image._x = image.x || 0;
	}

	image.x = context.x + image._x;
	image.y = context.y;

	this.alignImage(image);

	addPageItem(page, {
		type: type || 'image',
		item: image
	}, index);

	context.moveDown(image._height);

	return position;
};

ElementWriter.prototype.addSVG = function (image, index) {
	return this.addImage(image, index, 'svg')
};

ElementWriter.prototype.addQr = function (qr, index) {
	var context = this.context;
	var page = context.getCurrentPage(),
		position = this.getCurrentPositionOnPage();

	if (!page || (qr.absolutePosition === undefined && context.availableHeight < qr._height)) {
		return false;
	}

	if (qr._x === undefined) {
		qr._x = qr.x || 0;
	}

	qr.x = context.x + qr._x;
	qr.y = context.y;

	this.alignImage(qr);

	for (var i = 0, l = qr._canvas.length; i < l; i++) {
		var vector = qr._canvas[i];
		vector.x += qr.x;
		vector.y += qr.y;
		this.addVector(vector, true, true, index);
	}

	context.moveDown(qr._height);

	return position;
};

ElementWriter.prototype.alignImage = function (image) {
	var width = this.context.availableWidth;
	var imageWidth = image._minWidth;
	var offset = 0;
	switch (image._alignment) {
		case 'right':
			offset = width - imageWidth;
			break;
		case 'center':
			offset = (width - imageWidth) / 2;
			break;
	}

	if (offset) {
		image.x = (image.x || 0) + offset;
	}
};

ElementWriter.prototype.alignCanvas = function (node) {
	var width = this.context.availableWidth;
	var canvasWidth = node._minWidth;
	var offset = 0;
	switch (node._alignment) {
		case 'right':
			offset = width - canvasWidth;
			break;
		case 'center':
			offset = (width - canvasWidth) / 2;
			break;
	}
	if (offset) {
		node.canvas.forEach(function (vector) {
			offsetVector(vector, offset, 0);
		});
	}
};

ElementWriter.prototype.addVector = function (vector, ignoreContextX, ignoreContextY, index) {
	var context = this.context;
	var page = context.getCurrentPage(),
		position = this.getCurrentPositionOnPage();

	if (page) {
		offsetVector(vector, ignoreContextX ? 0 : context.x, ignoreContextY ? 0 : context.y);
		addPageItem(page, {
			type: 'vector',
			item: vector
		}, index);
		return position;
	}
};

ElementWriter.prototype.beginClip = function (width, height) {
	var ctx = this.context;
	var page = ctx.getCurrentPage();
	page.items.push({
		type: 'beginClip',
		item: { x: ctx.x, y: ctx.y, width: width, height: height }
	});
	return true;
};

ElementWriter.prototype.endClip = function () {
	var ctx = this.context;
	var page = ctx.getCurrentPage();
	page.items.push({
		type: 'endClip'
	});
	return true;
};

function cloneLine(line) {
	var result = new Line(line.maxWidth);

	for (var key in line) {
		if (line.hasOwnProperty(key)) {
			result[key] = line[key];
		}
	}

	return result;
}

ElementWriter.prototype.addFragment = function (block, useBlockXOffset, useBlockYOffset, dontUpdateContextPosition) {
	var ctx = this.context;
	var page = ctx.getCurrentPage();

	if (!useBlockXOffset && block.height > ctx.availableHeight) {
		return false;
	}

	block.items.forEach(function (item) {
		switch (item.type) {
			case 'line':
				var l = cloneLine(item.item);

				if (l._node) {
					l._node.positions[0].pageNumber = ctx.page + 1;
				}
				l.x = (l.x || 0) + (useBlockXOffset ? (block.xOffset || 0) : ctx.x);
				l.y = (l.y || 0) + (useBlockYOffset ? (block.yOffset || 0) : ctx.y);

				page.items.push({
					type: 'line',
					item: l
				});
				break;

			case 'vector':
				var v = pack(item.item);

				offsetVector(v, useBlockXOffset ? (block.xOffset || 0) : ctx.x, useBlockYOffset ? (block.yOffset || 0) : ctx.y);
				page.items.push({
					type: 'vector',
					item: v
				});
				break;

			case 'image':
			case 'svg':
				var img = pack(item.item);

				img.x = (img.x || 0) + (useBlockXOffset ? (block.xOffset || 0) : ctx.x);
				img.y = (img.y || 0) + (useBlockYOffset ? (block.yOffset || 0) : ctx.y);

				page.items.push({
					type: item.type,
					item: img
				});
				break;
		}
	});

	if (!dontUpdateContextPosition) {
		ctx.moveDown(block.height);
	}

	return true;
};

/**
 * Pushes the provided context onto the stack or creates a new one
 *
 * pushContext(context) - pushes the provided context and makes it current
 * pushContext(width, height) - creates and pushes a new context with the specified width and height
 * pushContext() - creates a new context for unbreakable blocks (with current availableWidth and full-page-height)
 */
ElementWriter.prototype.pushContext = function (contextOrWidth, height) {
	if (contextOrWidth === undefined) {
		height = this.context.getCurrentPage().height - this.context.pageMargins.top - this.context.pageMargins.bottom;
		contextOrWidth = this.context.availableWidth;
	}

	if (isNumber(contextOrWidth)) {
		contextOrWidth = new DocumentContext({ width: contextOrWidth, height: height }, { left: 0, right: 0, top: 0, bottom: 0 });
	}

	this.contextStack.push(this.context);
	this.context = contextOrWidth;
};

ElementWriter.prototype.popContext = function () {
	this.context = this.contextStack.pop();
};

ElementWriter.prototype.getCurrentPositionOnPage = function () {
	return (this.contextStack[0] || this.context).getCurrentPosition();
};


module.exports = ElementWriter;


/***/ }),
/* 539 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var ColumnCalculator = __webpack_require__(195);
var isFunction = __webpack_require__(0).isFunction;
var isNumber = __webpack_require__(0).isNumber;

function TableProcessor(tableNode) {
	this.tableNode = tableNode;
}

TableProcessor.prototype.beginTable = function (writer) {
	var tableNode;
	var availableWidth;
	var self = this;

	tableNode = this.tableNode;
	this.offsets = tableNode._offsets;
	this.layout = tableNode._layout;

	availableWidth = writer.context().availableWidth - this.offsets.total;
	ColumnCalculator.buildColumnWidths(tableNode.table.widths, availableWidth);

	this.tableWidth = tableNode._offsets.total + getTableInnerContentWidth();
	this.rowSpanData = prepareRowSpanData();
	this.cleanUpRepeatables = false;

	this.headerRows = tableNode.table.headerRows || 0;
	this.rowsWithoutPageBreak = this.headerRows + (tableNode.table.keepWithHeaderRows || 0);
	this.dontBreakRows = tableNode.table.dontBreakRows || false;

	if (this.rowsWithoutPageBreak) {
		writer.beginUnbreakableBlock();
	}

	// update the border properties of all cells before drawing any lines
	prepareCellBorders(this.tableNode.table.body);

	this.drawHorizontalLine(0, writer);

	function getTableInnerContentWidth() {
		var width = 0;

		tableNode.table.widths.forEach(function (w) {
			width += w._calcWidth;
		});

		return width;
	}

	function prepareRowSpanData() {
		var rsd = [];
		var x = 0;
		var lastWidth = 0;

		rsd.push({ left: 0, rowSpan: 0 });

		for (var i = 0, l = self.tableNode.table.body[0].length; i < l; i++) {
			var paddings = self.layout.paddingLeft(i, self.tableNode) + self.layout.paddingRight(i, self.tableNode);
			var lBorder = self.layout.vLineWidth(i, self.tableNode);
			lastWidth = paddings + lBorder + self.tableNode.table.widths[i]._calcWidth;
			rsd[rsd.length - 1].width = lastWidth;
			x += lastWidth;
			rsd.push({ left: x, rowSpan: 0, width: 0 });
		}

		return rsd;
	}

	// Iterate through all cells. If the current cell is the start of a
	// rowSpan/colSpan, update the border property of the cells on its
	// bottom/right accordingly. This is needed since each iteration of the
	// line-drawing loops draws lines for a single cell, not for an entire
	// rowSpan/colSpan.
	function prepareCellBorders(body) {
		for (var rowIndex = 0; rowIndex < body.length; rowIndex++) {
			var row = body[rowIndex];

			for (var colIndex = 0; colIndex < row.length; colIndex++) {
				var cell = row[colIndex];

				if (cell.border) {
					var rowSpan = cell.rowSpan || 1;
					var colSpan = cell.colSpan || 1;

					for (var rowOffset = 0; rowOffset < rowSpan; rowOffset++) {
						// set left border
						if (cell.border[0] !== undefined && rowOffset > 0) {
							setBorder(rowIndex + rowOffset, colIndex, 0, cell.border[0]);
						}

						// set right border
						if (cell.border[2] !== undefined) {
							setBorder(rowIndex + rowOffset, colIndex + colSpan - 1, 2, cell.border[2]);
						}
					}

					for (var colOffset = 0; colOffset < colSpan; colOffset++) {
						// set top border
						if (cell.border[1] !== undefined && colOffset > 0) {
							setBorder(rowIndex, colIndex + colOffset, 1, cell.border[1]);
						}

						// set bottom border
						if (cell.border[3] !== undefined) {
							setBorder(rowIndex + rowSpan - 1, colIndex + colOffset, 3, cell.border[3]);
						}
					}
				}
			}
		}

		// helper function to set the border for a given cell
		function setBorder(rowIndex, colIndex, borderIndex, borderValue) {
			var cell = body[rowIndex][colIndex];
			cell.border = cell.border || {};
			cell.border[borderIndex] = borderValue;
		}
	}
};

TableProcessor.prototype.onRowBreak = function (rowIndex, writer) {
	var self = this;
	return function () {
		var offset = self.rowPaddingTop + (!self.headerRows ? self.topLineWidth : 0);
		writer.context().availableHeight -= self.reservedAtBottom;
		writer.context().moveDown(offset);
	};
};

TableProcessor.prototype.beginRow = function (rowIndex, writer) {
	this.topLineWidth = this.layout.hLineWidth(rowIndex, this.tableNode);
	this.rowPaddingTop = this.layout.paddingTop(rowIndex, this.tableNode);
	this.bottomLineWidth = this.layout.hLineWidth(rowIndex + 1, this.tableNode);
	this.rowPaddingBottom = this.layout.paddingBottom(rowIndex, this.tableNode);

	this.rowCallback = this.onRowBreak(rowIndex, writer);
	writer.tracker.startTracking('pageChanged', this.rowCallback);
	if (this.dontBreakRows) {
		writer.beginUnbreakableBlock();
	}
	this.rowTopY = writer.context().y;
	this.reservedAtBottom = this.bottomLineWidth + this.rowPaddingBottom;

	writer.context().availableHeight -= this.reservedAtBottom;

	writer.context().moveDown(this.rowPaddingTop);
};

TableProcessor.prototype.drawHorizontalLine = function (lineIndex, writer, overrideY) {
	var lineWidth = this.layout.hLineWidth(lineIndex, this.tableNode);
	if (lineWidth) {
		var style = this.layout.hLineStyle(lineIndex, this.tableNode);
		var dash;
		if (style && style.dash) {
			dash = style.dash;
		}

		var offset = lineWidth / 2;
		var currentLine = null;
		var body = this.tableNode.table.body;
		var cellAbove;
		var currentCell;
		var rowCellAbove;

		for (var i = 0, l = this.rowSpanData.length; i < l; i++) {
			var data = this.rowSpanData[i];
			var shouldDrawLine = !data.rowSpan;
			var borderColor = null;

			// draw only if the current cell requires a top border or the cell in the
			// row above requires a bottom border
			if (shouldDrawLine && i < l - 1) {
				var topBorder = false, bottomBorder = false, rowBottomBorder = false;

				// the cell in the row above
				if (lineIndex > 0) {
					cellAbove = body[lineIndex - 1][i];
					bottomBorder = cellAbove.border ? cellAbove.border[3] : this.layout.defaultBorder;
					if (bottomBorder && cellAbove.borderColor) {
						borderColor = cellAbove.borderColor[3];
					}
				}

				// the current cell
				if (lineIndex < body.length) {
					currentCell = body[lineIndex][i];
					topBorder = currentCell.border ? currentCell.border[1] : this.layout.defaultBorder;
					if (topBorder && borderColor == null && currentCell.borderColor) {
						borderColor = currentCell.borderColor[1];
					}
				}

				shouldDrawLine = topBorder || bottomBorder;
			}

			if (cellAbove && cellAbove._rowSpanCurrentOffset) {
				rowCellAbove = body[lineIndex - 1 - cellAbove._rowSpanCurrentOffset][i];
				rowBottomBorder = rowCellAbove && rowCellAbove.border ? rowCellAbove.border[3] : this.layout.defaultBorder;
				if (rowBottomBorder && rowCellAbove && rowCellAbove.borderColor) {
					borderColor = rowCellAbove.borderColor[3];
				}
			}

			if (borderColor == null) {
				borderColor = isFunction(this.layout.hLineColor) ? this.layout.hLineColor(lineIndex, this.tableNode, i) : this.layout.hLineColor;
			}

			if (!currentLine && shouldDrawLine) {
				currentLine = { left: data.left, width: 0 };
			}

			if (shouldDrawLine) {
				var colSpanIndex = 0;
				if (rowCellAbove && rowCellAbove.colSpan && rowBottomBorder) {
					while (rowCellAbove.colSpan > colSpanIndex) {
						currentLine.width += (this.rowSpanData[i + colSpanIndex++].width || 0);
					}
					i += colSpanIndex - 1;
				} else if (cellAbove && cellAbove.colSpan && bottomBorder) {
					while (cellAbove.colSpan > colSpanIndex) {
						currentLine.width += (this.rowSpanData[i + colSpanIndex++].width || 0);
					}
					i += colSpanIndex - 1;
				} else if (currentCell && currentCell.colSpan && topBorder) {
					while (currentCell.colSpan > colSpanIndex) {
						currentLine.width += (this.rowSpanData[i + colSpanIndex++].width || 0);
					}
					i += colSpanIndex - 1;
				} else {
					currentLine.width += (this.rowSpanData[i].width || 0);
				}
			}

			var y = (overrideY || 0) + offset;


			if (shouldDrawLine) {
				if (currentLine && currentLine.width) {
					writer.addVector({
						type: 'line',
						x1: currentLine.left,
						x2: currentLine.left + currentLine.width,
						y1: y,
						y2: y,
						lineWidth: lineWidth,
						dash: dash,
						lineColor: borderColor
					}, false, overrideY);
					currentLine = null;
					borderColor = null;
					cellAbove = null;
					currentCell = null;
					rowCellAbove = null;
				}
			}
		}

		writer.context().moveDown(lineWidth);
	}
};

TableProcessor.prototype.drawVerticalLine = function (x, y0, y1, vLineColIndex, writer, vLineRowIndex, beforeVLineColIndex) {
	var width = this.layout.vLineWidth(vLineColIndex, this.tableNode);
	if (width === 0) {
		return;
	}
	var style = this.layout.vLineStyle(vLineColIndex, this.tableNode);
	var dash;
	if (style && style.dash) {
		dash = style.dash;
	}

	var body = this.tableNode.table.body;
	var cellBefore;
	var currentCell;
	var borderColor;

	// the cell in the col before
	if (vLineColIndex > 0) {
		cellBefore = body[vLineRowIndex][beforeVLineColIndex];
		if (cellBefore && cellBefore.borderColor) {
			if (cellBefore.border ? cellBefore.border[2] : this.layout.defaultBorder) {
				borderColor = cellBefore.borderColor[2];
			}
		}
	}

	// the current cell
	if (borderColor == null && vLineColIndex < body.length) {
		currentCell = body[vLineRowIndex][vLineColIndex];
		if (currentCell && currentCell.borderColor) {
			if (currentCell.border ? currentCell.border[0] : this.layout.defaultBorder) {
				borderColor = currentCell.borderColor[0];
			}
		}
	}

	if (borderColor == null && cellBefore && cellBefore._rowSpanCurrentOffset) {
		var rowCellBeforeAbove = body[vLineRowIndex - cellBefore._rowSpanCurrentOffset][beforeVLineColIndex];
		if (rowCellBeforeAbove.borderColor) {
			if (rowCellBeforeAbove.border ? rowCellBeforeAbove.border[2] : this.layout.defaultBorder) {
				borderColor = rowCellBeforeAbove.borderColor[2];
			}
		}
	}

	if (borderColor == null && currentCell && currentCell._rowSpanCurrentOffset) {
		var rowCurrentCellAbove = body[vLineRowIndex - currentCell._rowSpanCurrentOffset][vLineColIndex];
		if (rowCurrentCellAbove.borderColor) {
			if (rowCurrentCellAbove.border ? rowCurrentCellAbove.border[2] : this.layout.defaultBorder) {
				borderColor = rowCurrentCellAbove.borderColor[2];
			}
		}
	}

	if (borderColor == null) {
		borderColor = isFunction(this.layout.vLineColor) ? this.layout.vLineColor(vLineColIndex, this.tableNode, vLineRowIndex) : this.layout.vLineColor;
	}
	writer.addVector({
		type: 'line',
		x1: x + width / 2,
		x2: x + width / 2,
		y1: y0,
		y2: y1,
		lineWidth: width,
		dash: dash,
		lineColor: borderColor
	}, false, true);
	cellBefore = null;
	currentCell = null;
	borderColor = null;
};

TableProcessor.prototype.endTable = function (writer) {
	if (this.cleanUpRepeatables) {
		writer.popFromRepeatables();
	}
};

TableProcessor.prototype.endRow = function (rowIndex, writer, pageBreaks) {
	var l, i;
	var self = this;
	writer.tracker.stopTracking('pageChanged', this.rowCallback);
	writer.context().moveDown(this.layout.paddingBottom(rowIndex, this.tableNode));
	writer.context().availableHeight += this.reservedAtBottom;

	var endingPage = writer.context().page;
	var endingY = writer.context().y;

	var xs = getLineXs();

	var ys = [];

	var hasBreaks = pageBreaks && pageBreaks.length > 0;
	var body = this.tableNode.table.body;

	ys.push({
		y0: this.rowTopY,
		page: hasBreaks ? pageBreaks[0].prevPage : endingPage
	});

	if (hasBreaks) {
		for (i = 0, l = pageBreaks.length; i < l; i++) {
			var pageBreak = pageBreaks[i];
			ys[ys.length - 1].y1 = pageBreak.prevY;

			ys.push({ y0: pageBreak.y, page: pageBreak.prevPage + 1 });
		}
	}

	ys[ys.length - 1].y1 = endingY;

	var skipOrphanePadding = (ys[0].y1 - ys[0].y0 === this.rowPaddingTop);
	for (var yi = (skipOrphanePadding ? 1 : 0), yl = ys.length; yi < yl; yi++) {
		var willBreak = yi < ys.length - 1;
		var rowBreakWithoutHeader = (yi > 0 && !this.headerRows);
		var hzLineOffset = rowBreakWithoutHeader ? 0 : this.topLineWidth;
		var y1 = ys[yi].y0;
		var y2 = ys[yi].y1;

		if (willBreak) {
			y2 = y2 + this.rowPaddingBottom;
		}

		if (writer.context().page != ys[yi].page) {
			writer.context().page = ys[yi].page;

			//TODO: buggy, availableHeight should be updated on every pageChanged event
			// TableProcessor should be pageChanged listener, instead of processRow
			this.reservedAtBottom = 0;
		}

		for (i = 0, l = xs.length; i < l; i++) {
			var leftCellBorder = false;
			var rightCellBorder = false;
			var colIndex = xs[i].index;

			// current cell
			if (colIndex < body[rowIndex].length) {
				var cell = body[rowIndex][colIndex];
				leftCellBorder = cell.border ? cell.border[0] : this.layout.defaultBorder;
				rightCellBorder = cell.border ? cell.border[2] : this.layout.defaultBorder;
			}

			// before cell
			if (colIndex > 0 && !leftCellBorder) {
				var cell = body[rowIndex][colIndex - 1];
				leftCellBorder = cell.border ? cell.border[2] : this.layout.defaultBorder;
			}

			// after cell
			if (colIndex + 1 < body[rowIndex].length && !rightCellBorder) {
				var cell = body[rowIndex][colIndex + 1];
				rightCellBorder = cell.border ? cell.border[0] : this.layout.defaultBorder;
			}

			if (leftCellBorder) {
				this.drawVerticalLine(xs[i].x, y1 - hzLineOffset, y2 + this.bottomLineWidth, xs[i].index, writer, rowIndex, xs[i - 1] ? xs[i - 1].index : null);
			}

			if (i < l - 1) {
				var fillColor = body[rowIndex][colIndex].fillColor;
				var fillOpacity = body[rowIndex][colIndex].fillOpacity;
				if (!fillColor) {
					fillColor = isFunction(this.layout.fillColor) ? this.layout.fillColor(rowIndex, this.tableNode, colIndex) : this.layout.fillColor;
				}
				if (!isNumber(fillOpacity)) {
					fillOpacity = isFunction(this.layout.fillOpacity) ? this.layout.fillOpacity(rowIndex, this.tableNode, colIndex) : this.layout.fillOpacity;
                                }
				if (fillColor) {
					var widthLeftBorder = leftCellBorder ? this.layout.vLineWidth(colIndex, this.tableNode) : 0;
					var widthRightBorder;
					if ((colIndex === 0 || colIndex + 1 == body[rowIndex].length) && !rightCellBorder) {
						widthRightBorder = this.layout.vLineWidth(colIndex + 1, this.tableNode);
					} else if (rightCellBorder) {
						widthRightBorder = this.layout.vLineWidth(colIndex + 1, this.tableNode) / 2;
					} else {
						widthRightBorder = 0;
					}

					var x1f = this.dontBreakRows ? xs[i].x + widthLeftBorder : xs[i].x + (widthLeftBorder / 2);
					var y1f = this.dontBreakRows ? y1 : y1 - (hzLineOffset / 2);
					var x2f = xs[i + 1].x + widthRightBorder;
					var y2f = this.dontBreakRows ? y2 + this.bottomLineWidth : y2 + (this.bottomLineWidth / 2);
					writer.addVector({
						type: 'rect',
						x: x1f,
						y: y1f,
						w: x2f - x1f,
						h: y2f - y1f,
						lineWidth: 0,
						color: fillColor,
						fillOpacity: fillOpacity
					}, false, true, writer.context().backgroundLength[writer.context().page]);
				}
			}
		}

		if (willBreak && this.layout.hLineWhenBroken !== false) {
			this.drawHorizontalLine(rowIndex + 1, writer, y2);
		}
		if (rowBreakWithoutHeader && this.layout.hLineWhenBroken !== false) {
			this.drawHorizontalLine(rowIndex, writer, y1);
		}
	}

	writer.context().page = endingPage;
	writer.context().y = endingY;

	var row = this.tableNode.table.body[rowIndex];
	for (i = 0, l = row.length; i < l; i++) {
		if (row[i].rowSpan) {
			this.rowSpanData[i].rowSpan = row[i].rowSpan;

			// fix colSpans
			if (row[i].colSpan && row[i].colSpan > 1) {
				for (var j = 1; j < row[i].rowSpan; j++) {
					this.tableNode.table.body[rowIndex + j][i]._colSpan = row[i].colSpan;
				}
			}
			// fix rowSpans
			if (row[i].rowSpan && row[i].rowSpan > 1) {
				for (var j = 1; j < row[i].rowSpan; j++) {
					this.tableNode.table.body[rowIndex + j][i]._rowSpanCurrentOffset = j;
				}
			}
		}

		if (this.rowSpanData[i].rowSpan > 0) {
			this.rowSpanData[i].rowSpan--;
		}
	}

	this.drawHorizontalLine(rowIndex + 1, writer);

	if (this.headerRows && rowIndex === this.headerRows - 1) {
		this.headerRepeatable = writer.currentBlockToRepeatable();
	}

	if (this.dontBreakRows) {
		writer.tracker.auto('pageChanged',
			function () {
				if (!self.headerRows && self.layout.hLineWhenBroken !== false) {
					self.drawHorizontalLine(rowIndex, writer);
				}
			},
			function () {
				writer.commitUnbreakableBlock();
			}
		);
	}

	if (this.headerRepeatable && (rowIndex === (this.rowsWithoutPageBreak - 1) || rowIndex === this.tableNode.table.body.length - 1)) {
		writer.commitUnbreakableBlock();
		writer.pushToRepeatables(this.headerRepeatable);
		this.cleanUpRepeatables = true;
		this.headerRepeatable = null;
	}

	function getLineXs() {
		var result = [];
		var cols = 0;

		for (var i = 0, l = self.tableNode.table.body[rowIndex].length; i < l; i++) {
			if (!cols) {
				result.push({ x: self.rowSpanData[i].left, index: i });

				var item = self.tableNode.table.body[rowIndex][i];
				cols = (item._colSpan || item.colSpan || 0);
			}
			if (cols > 0) {
				cols--;
			}
		}

		result.push({ x: self.rowSpanData[self.rowSpanData.length - 1].left, index: self.rowSpanData.length - 1 });

		return result;
	}
};

module.exports = TableProcessor;


/***/ }),
/* 540 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


module.exports = {
	'4A0': [4767.87, 6740.79],
	'2A0': [3370.39, 4767.87],
	A0: [2383.94, 3370.39],
	A1: [1683.78, 2383.94],
	A2: [1190.55, 1683.78],
	A3: [841.89, 1190.55],
	A4: [595.28, 841.89],
	A5: [419.53, 595.28],
	A6: [297.64, 419.53],
	A7: [209.76, 297.64],
	A8: [147.40, 209.76],
	A9: [104.88, 147.40],
	A10: [73.70, 104.88],
	B0: [2834.65, 4008.19],
	B1: [2004.09, 2834.65],
	B2: [1417.32, 2004.09],
	B3: [1000.63, 1417.32],
	B4: [708.66, 1000.63],
	B5: [498.90, 708.66],
	B6: [354.33, 498.90],
	B7: [249.45, 354.33],
	B8: [175.75, 249.45],
	B9: [124.72, 175.75],
	B10: [87.87, 124.72],
	C0: [2599.37, 3676.54],
	C1: [1836.85, 2599.37],
	C2: [1298.27, 1836.85],
	C3: [918.43, 1298.27],
	C4: [649.13, 918.43],
	C5: [459.21, 649.13],
	C6: [323.15, 459.21],
	C7: [229.61, 323.15],
	C8: [161.57, 229.61],
	C9: [113.39, 161.57],
	C10: [79.37, 113.39],
	RA0: [2437.80, 3458.27],
	RA1: [1729.13, 2437.80],
	RA2: [1218.90, 1729.13],
	RA3: [864.57, 1218.90],
	RA4: [609.45, 864.57],
	SRA0: [2551.18, 3628.35],
	SRA1: [1814.17, 2551.18],
	SRA2: [1275.59, 1814.17],
	SRA3: [907.09, 1275.59],
	SRA4: [637.80, 907.09],
	EXECUTIVE: [521.86, 756.00],
	FOLIO: [612.00, 936.00],
	LEGAL: [612.00, 1008.00],
	LETTER: [612.00, 792.00],
	TABLOID: [792.00, 1224.00]
};


/***/ }),
/* 541 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(Buffer) {

var fs = __webpack_require__(53);

function ImageMeasure(pdfKitDoc, imageDictionary) {
	this.pdfKitDoc = pdfKitDoc;
	this.imageDictionary = imageDictionary || {};
}

ImageMeasure.prototype.measureImage = function (src) {
	var image;
	var that = this;

	if (!this.pdfKitDoc._imageRegistry[src]) {
		try {
			image = this.pdfKitDoc.openImage(realImageSrc(src));
			if (!image) {
				throw 'No image';
			}
		} catch (error) {
			throw 'Invalid image: ' + error.toString() + '\nImages dictionary should contain dataURL entries (or local file paths in node.js)';
		}
		image.embed(this.pdfKitDoc);
		this.pdfKitDoc._imageRegistry[src] = image;
	} else {
		image = this.pdfKitDoc._imageRegistry[src];
	}

	return { width: image.width, height: image.height };

	function realImageSrc(src) {
		var img = that.imageDictionary[src];

		if (!img) {
			return src;
		}

		if (fs.existsSync(img)) {
			return fs.readFileSync(img);
		}

		var index = img.indexOf('base64,');
		if (index < 0) {
			return that.imageDictionary[src];
		}

		return Buffer.from(img.substring(index + 7), 'base64');
	}
};

module.exports = ImageMeasure;

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer))

/***/ }),
/* 542 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var xmldoc = __webpack_require__(543);

/** Strip unit postfix, parse number, but return undefined instead of NaN for bad input */
function stripUnits(textVal) {
	var n = parseFloat(textVal);
	if (typeof n !== 'number' || isNaN(n)) {
		return undefined;
	}
	return n;
}

/** Make sure it's valid XML and the root tage is <svg/>, returns xmldoc DOM */
function parseSVG(svgString) {
	var doc;
	
	try {
		doc = new xmldoc.XmlDocument(svgString);
	} catch (err) {
		throw new Error('SVGMeasure: ' + err);
	}

	if (doc.name !== "svg") {
		throw new Error('SVGMeasure: expected <svg> document');
	}

	return doc;
}

function SVGMeasure() {
}

SVGMeasure.prototype.measureSVG = function (svgString) {

	var doc = parseSVG(svgString);

	var docWidth = stripUnits(doc.attr.width);
	var docHeight = stripUnits(doc.attr.height);

	if ((docWidth == undefined || docHeight == undefined) && typeof doc.attr.viewBox == 'string') {
		var viewBoxParts = doc.attr.viewBox.split(/[,\s]+/);
		if (viewBoxParts.length !== 4) {
			throw new Error("Unexpected svg viewbox format, should have 4 entries but found: '" + doc.attr.viewBox + "'");
		}
		if (docWidth == undefined) {
			docWidth = stripUnits(viewBoxParts[2]);
		}
		if (docHeight == undefined) {
			docHeight = stripUnits(viewBoxParts[3]);
		}
	}

	return {
		width: docWidth,
		height: docHeight
	};
};

SVGMeasure.prototype.writeDimensions = function (svgString, dimensions) {

	var doc = parseSVG(svgString);

	doc.attr.width = "" + dimensions.width;
	doc.attr.height = "" + dimensions.height;

	return doc.toString();
};

module.exports = SVGMeasure;


/***/ }),
/* 543 */
/***/ (function(module, exports, __webpack_require__) {

// This file is just added for convenience so this repository can be
// directly checked out into a project's deps folder
module.exports = __webpack_require__(544);


/***/ }),
/* 544 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(global) {(function () {

var sax;

if ( true && module.exports && !global.xmldocAssumeBrowser) {
  // We're being used in a Node-like environment
  sax = __webpack_require__(545);
}
else {
  // assume it's attached to the Window object in a browser
  sax = this.sax;

  if (!sax) // no sax for you!
    throw new Error("Expected sax to be defined. Make sure you're including sax.js before this file.");
}

/*
XmlElement is our basic building block. Everything is an XmlElement; even XmlDocument
behaves like an XmlElement by inheriting its attributes and functions.
*/

function XmlElement(tag) {
  // Capture the parser object off of the XmlDocument delegate
  var parser = delegates[delegates.length - 1].parser;

  this.name = tag.name;
  this.attr = tag.attributes;
  this.val = "";
  this.children = [];
  this.firstChild = null;
  this.lastChild = null;

  // Assign parse information
  this.line = parser.line;
  this.column = parser.column;
  this.position = parser.position;
  this.startTagPosition = parser.startTagPosition;
}

// Private methods

XmlElement.prototype._addChild = function(child) {
  // add to our children array
  this.children.push(child);

  // update first/last pointers
  if (!this.firstChild) this.firstChild = child;
  this.lastChild = child;
};

// SaxParser handlers

XmlElement.prototype._opentag = function(tag) {

  var child = new XmlElement(tag);

  this._addChild(child);

  delegates.unshift(child);
};

XmlElement.prototype._closetag = function() {
  delegates.shift();
};

XmlElement.prototype._text = function(text) {
  if (typeof this.children === 'undefined')
    return

  this.val += text;

  this._addChild(new XmlTextNode(text));
};

XmlElement.prototype._cdata = function(cdata) {
  this.val += cdata;

  this._addChild(new XmlCDataNode(cdata));
};

XmlElement.prototype._comment = function(comment) {
  if (typeof this.children === 'undefined')
    return

  this._addChild(new XmlCommentNode(comment));
};

XmlElement.prototype._error = function(err) {
  throw err;
};

// Useful functions

XmlElement.prototype.eachChild = function(iterator, context) {
  for (var i=0, l=this.children.length; i<l; i++)
    if (this.children[i].type === "element")
      if (iterator.call(context, this.children[i], i, this.children) === false) return;
};

XmlElement.prototype.childNamed = function(name) {
  for (var i=0, l=this.children.length; i<l; i++) {
    var child = this.children[i];
    if (child.name === name) return child;
  }
  return undefined;
};

XmlElement.prototype.childrenNamed = function(name) {
  var matches = [];

  for (var i=0, l=this.children.length; i<l; i++)
    if (this.children[i].name === name)
      matches.push(this.children[i]);

  return matches;
};

XmlElement.prototype.childWithAttribute = function(name,value) {
  for (var i=0, l=this.children.length; i<l; i++) {
    var child = this.children[i];
    if (child.type === "element" && ((value && child.attr[name] === value) || (!value && child.attr[name])))
      return child;
  }
  return undefined;
};

XmlElement.prototype.descendantWithPath = function(path) {
  var descendant = this;
  var components = path.split('.');

  for (var i=0, l=components.length; i<l; i++)
    if (descendant && descendant.type === "element")
      descendant = descendant.childNamed(components[i]);
    else
      return undefined;

  return descendant;
};

XmlElement.prototype.valueWithPath = function(path) {
  var components = path.split('@');
  var descendant = this.descendantWithPath(components[0]);
  if (descendant)
    return components.length > 1 ? descendant.attr[components[1]] : descendant.val;
  else
    return undefined;
};

// String formatting (for debugging)

XmlElement.prototype.toString = function(options) {
  return this.toStringWithIndent("", options);
};

XmlElement.prototype.toStringWithIndent = function(indent, options) {
  var s = indent + "<" + this.name;
  var linebreak = options && options.compressed ? "" : "\n";
  var preserveWhitespace = options && options.preserveWhitespace;

  for (var name in this.attr)
    if (Object.prototype.hasOwnProperty.call(this.attr, name))
        s += " " + name + '="' + escapeXML(this.attr[name]) + '"';

  if (this.children.length === 1 && this.children[0].type !== "element") {
    s += ">" + this.children[0].toString(options) + "</" + this.name + ">";
  }
  else if (this.children.length) {
    s += ">" + linebreak;

    var childIndent = indent + (options && options.compressed ? "" : "  ");

    for (var i=0, l=this.children.length; i<l; i++) {
      s += this.children[i].toStringWithIndent(childIndent, options) + linebreak;
    }

    s += indent + "</" + this.name + ">";
  }
  else if (options && options.html) {
    var whiteList = [
      "area", "base", "br", "col", "embed", "frame", "hr", "img", "input",
      "keygen", "link", "menuitem", "meta", "param", "source", "track", "wbr"
    ];
    if (whiteList.indexOf(this.name) !== -1) s += "/>";
    else s += "></" + this.name + ">";
  }
  else {
    s += "/>";
  }

  return s;
};

// Alternative XML nodes

function XmlTextNode (text) {
  this.text = text;
}

XmlTextNode.prototype.toString = function(options) {
  return formatText(escapeXML(this.text), options);
};

XmlTextNode.prototype.toStringWithIndent = function(indent, options) {
  return indent+this.toString(options);
};

function XmlCDataNode (cdata) {
  this.cdata = cdata;
}

XmlCDataNode.prototype.toString = function(options) {
  return "<![CDATA["+formatText(this.cdata, options)+"]]>";
};

XmlCDataNode.prototype.toStringWithIndent = function(indent, options) {
  return indent+this.toString(options);
};

function XmlCommentNode (comment) {
  this.comment = comment;
}

XmlCommentNode.prototype.toString = function(options) {
  return "<!--"+formatText(escapeXML(this.comment), options)+"-->";
};

XmlCommentNode.prototype.toStringWithIndent = function(indent, options) {
  return indent+this.toString(options);
};

// Node type tag

XmlElement.prototype.type = "element";
XmlTextNode.prototype.type = "text";
XmlCDataNode.prototype.type = "cdata";
XmlCommentNode.prototype.type = "comment";

/*
XmlDocument is the class we expose to the user; it uses the sax parser to create a hierarchy
of XmlElements.
*/

function XmlDocument(xml) {
  xml && (xml = xml.toString().trim());

  if (!xml)
    throw new Error("No XML to parse!");

  // Stores doctype (if defined)
  this.doctype = "";

  // Expose the parser to the other delegates while the parser is running
  this.parser = sax.parser(true); // strict
  addParserEvents(this.parser);

  // We'll use the file-scoped "delegates" var to remember what elements we're currently
  // parsing; they will push and pop off the stack as we get deeper into the XML hierarchy.
  // It's safe to use a global because JS is single-threaded.
  delegates = [this];

  this.parser.write(xml);

  // Remove the parser as it is no longer needed and should not be exposed to clients
  delete this.parser;
}

// make XmlDocument inherit XmlElement's methods
extend(XmlDocument.prototype, XmlElement.prototype);

XmlDocument.prototype._opentag = function(tag) {
  if (typeof this.children === 'undefined')
    // the first tag we encounter should be the root - we'll "become" the root XmlElement
    XmlElement.call(this,tag);
  else
    // all other tags will be the root element's children
    XmlElement.prototype._opentag.apply(this,arguments);
};

XmlDocument.prototype._doctype = function(doctype) {
  this.doctype += doctype;
}

// file-scoped global stack of delegates
var delegates = null;

/*
Helper functions
*/

function addParserEvents(parser) {
  parser.onopentag = parser_opentag;
  parser.onclosetag = parser_closetag;
  parser.ontext = parser_text;
  parser.oncdata = parser_cdata;
  parser.oncomment = parser_comment;
  parser.ondoctype = parser_doctype;
  parser.onerror = parser_error;
}

// create these closures and cache them by keeping them file-scoped
function parser_opentag() { delegates[0] && delegates[0]._opentag.apply(delegates[0],arguments) }
function parser_closetag() { delegates[0] && delegates[0]._closetag.apply(delegates[0],arguments) }
function parser_text() { delegates[0] && delegates[0]._text.apply(delegates[0],arguments) }
function parser_cdata() { delegates[0] && delegates[0]._cdata.apply(delegates[0],arguments) }
function parser_comment() { delegates[0] && delegates[0]._comment.apply(delegates[0],arguments) }
function parser_doctype() { delegates[0] && delegates[0]._doctype.apply(delegates[0],arguments) }
function parser_error() { delegates[0] && delegates[0]._error.apply(delegates[0],arguments) }

// a relatively standard extend method
function extend(destination, source) {
  for (var prop in source)
    if (source.hasOwnProperty(prop))
      destination[prop] = source[prop];
}

// escapes XML entities like "<", "&", etc.
function escapeXML(value){
  return value.toString().replace(/&/g, '&amp;').replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/'/g, '&apos;').replace(/"/g, '&quot;');
}

// formats some text for debugging given a few options
function formatText(text, options) {
  var finalText = text;

  if (options && options.trimmed && text.length > 25)
    finalText = finalText.substring(0,25).trim() + "…";
  if (!(options && options.preserveWhitespace))
    finalText = finalText.trim();

  return finalText;
}

// Are we being used in a Node-like environment?
if ( true && module.exports && !global.xmldocAssumeBrowser)
    module.exports.XmlDocument = XmlDocument;
else
    this.XmlDocument = XmlDocument;

})();

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(24)))

/***/ }),
/* 545 */
/***/ (function(module, exports, __webpack_require__) {

/* WEBPACK VAR INJECTION */(function(Buffer) {;(function (sax) { // wrapper for non-node envs
  sax.parser = function (strict, opt) { return new SAXParser(strict, opt) }
  sax.SAXParser = SAXParser
  sax.SAXStream = SAXStream
  sax.createStream = createStream

  // When we pass the MAX_BUFFER_LENGTH position, start checking for buffer overruns.
  // When we check, schedule the next check for MAX_BUFFER_LENGTH - (max(buffer lengths)),
  // since that's the earliest that a buffer overrun could occur.  This way, checks are
  // as rare as required, but as often as necessary to ensure never crossing this bound.
  // Furthermore, buffers are only tested at most once per write(), so passing a very
  // large string into write() might have undesirable effects, but this is manageable by
  // the caller, so it is assumed to be safe.  Thus, a call to write() may, in the extreme
  // edge case, result in creating at most one complete copy of the string passed in.
  // Set to Infinity to have unlimited buffers.
  sax.MAX_BUFFER_LENGTH = 64 * 1024

  var buffers = [
    'comment', 'sgmlDecl', 'textNode', 'tagName', 'doctype',
    'procInstName', 'procInstBody', 'entity', 'attribName',
    'attribValue', 'cdata', 'script'
  ]

  sax.EVENTS = [
    'text',
    'processinginstruction',
    'sgmldeclaration',
    'doctype',
    'comment',
    'opentagstart',
    'attribute',
    'opentag',
    'closetag',
    'opencdata',
    'cdata',
    'closecdata',
    'error',
    'end',
    'ready',
    'script',
    'opennamespace',
    'closenamespace'
  ]

  function SAXParser (strict, opt) {
    if (!(this instanceof SAXParser)) {
      return new SAXParser(strict, opt)
    }

    var parser = this
    clearBuffers(parser)
    parser.q = parser.c = ''
    parser.bufferCheckPosition = sax.MAX_BUFFER_LENGTH
    parser.opt = opt || {}
    parser.opt.lowercase = parser.opt.lowercase || parser.opt.lowercasetags
    parser.looseCase = parser.opt.lowercase ? 'toLowerCase' : 'toUpperCase'
    parser.tags = []
    parser.closed = parser.closedRoot = parser.sawRoot = false
    parser.tag = parser.error = null
    parser.strict = !!strict
    parser.noscript = !!(strict || parser.opt.noscript)
    parser.state = S.BEGIN
    parser.strictEntities = parser.opt.strictEntities
    parser.ENTITIES = parser.strictEntities ? Object.create(sax.XML_ENTITIES) : Object.create(sax.ENTITIES)
    parser.attribList = []

    // namespaces form a prototype chain.
    // it always points at the current tag,
    // which protos to its parent tag.
    if (parser.opt.xmlns) {
      parser.ns = Object.create(rootNS)
    }

    // mostly just for error reporting
    parser.trackPosition = parser.opt.position !== false
    if (parser.trackPosition) {
      parser.position = parser.line = parser.column = 0
    }
    emit(parser, 'onready')
  }

  if (!Object.create) {
    Object.create = function (o) {
      function F () {}
      F.prototype = o
      var newf = new F()
      return newf
    }
  }

  if (!Object.keys) {
    Object.keys = function (o) {
      var a = []
      for (var i in o) if (o.hasOwnProperty(i)) a.push(i)
      return a
    }
  }

  function checkBufferLength (parser) {
    var maxAllowed = Math.max(sax.MAX_BUFFER_LENGTH, 10)
    var maxActual = 0
    for (var i = 0, l = buffers.length; i < l; i++) {
      var len = parser[buffers[i]].length
      if (len > maxAllowed) {
        // Text/cdata nodes can get big, and since they're buffered,
        // we can get here under normal conditions.
        // Avoid issues by emitting the text node now,
        // so at least it won't get any bigger.
        switch (buffers[i]) {
          case 'textNode':
            closeText(parser)
            break

          case 'cdata':
            emitNode(parser, 'oncdata', parser.cdata)
            parser.cdata = ''
            break

          case 'script':
            emitNode(parser, 'onscript', parser.script)
            parser.script = ''
            break

          default:
            error(parser, 'Max buffer length exceeded: ' + buffers[i])
        }
      }
      maxActual = Math.max(maxActual, len)
    }
    // schedule the next check for the earliest possible buffer overrun.
    var m = sax.MAX_BUFFER_LENGTH - maxActual
    parser.bufferCheckPosition = m + parser.position
  }

  function clearBuffers (parser) {
    for (var i = 0, l = buffers.length; i < l; i++) {
      parser[buffers[i]] = ''
    }
  }

  function flushBuffers (parser) {
    closeText(parser)
    if (parser.cdata !== '') {
      emitNode(parser, 'oncdata', parser.cdata)
      parser.cdata = ''
    }
    if (parser.script !== '') {
      emitNode(parser, 'onscript', parser.script)
      parser.script = ''
    }
  }

  SAXParser.prototype = {
    end: function () { end(this) },
    write: write,
    resume: function () { this.error = null; return this },
    close: function () { return this.write(null) },
    flush: function () { flushBuffers(this) }
  }

  var Stream
  try {
    Stream = __webpack_require__(121).Stream
  } catch (ex) {
    Stream = function () {}
  }

  var streamWraps = sax.EVENTS.filter(function (ev) {
    return ev !== 'error' && ev !== 'end'
  })

  function createStream (strict, opt) {
    return new SAXStream(strict, opt)
  }

  function SAXStream (strict, opt) {
    if (!(this instanceof SAXStream)) {
      return new SAXStream(strict, opt)
    }

    Stream.apply(this)

    this._parser = new SAXParser(strict, opt)
    this.writable = true
    this.readable = true

    var me = this

    this._parser.onend = function () {
      me.emit('end')
    }

    this._parser.onerror = function (er) {
      me.emit('error', er)

      // if didn't throw, then means error was handled.
      // go ahead and clear error, so we can write again.
      me._parser.error = null
    }

    this._decoder = null

    streamWraps.forEach(function (ev) {
      Object.defineProperty(me, 'on' + ev, {
        get: function () {
          return me._parser['on' + ev]
        },
        set: function (h) {
          if (!h) {
            me.removeAllListeners(ev)
            me._parser['on' + ev] = h
            return h
          }
          me.on(ev, h)
        },
        enumerable: true,
        configurable: false
      })
    })
  }

  SAXStream.prototype = Object.create(Stream.prototype, {
    constructor: {
      value: SAXStream
    }
  })

  SAXStream.prototype.write = function (data) {
    if (typeof Buffer === 'function' &&
      typeof Buffer.isBuffer === 'function' &&
      Buffer.isBuffer(data)) {
      if (!this._decoder) {
        var SD = __webpack_require__(125).StringDecoder
        this._decoder = new SD('utf8')
      }
      data = this._decoder.write(data)
    }

    this._parser.write(data.toString())
    this.emit('data', data)
    return true
  }

  SAXStream.prototype.end = function (chunk) {
    if (chunk && chunk.length) {
      this.write(chunk)
    }
    this._parser.end()
    return true
  }

  SAXStream.prototype.on = function (ev, handler) {
    var me = this
    if (!me._parser['on' + ev] && streamWraps.indexOf(ev) !== -1) {
      me._parser['on' + ev] = function () {
        var args = arguments.length === 1 ? [arguments[0]] : Array.apply(null, arguments)
        args.splice(0, 0, ev)
        me.emit.apply(me, args)
      }
    }

    return Stream.prototype.on.call(me, ev, handler)
  }

  // this really needs to be replaced with character classes.
  // XML allows all manner of ridiculous numbers and digits.
  var CDATA = '[CDATA['
  var DOCTYPE = 'DOCTYPE'
  var XML_NAMESPACE = 'http://www.w3.org/XML/1998/namespace'
  var XMLNS_NAMESPACE = 'http://www.w3.org/2000/xmlns/'
  var rootNS = { xml: XML_NAMESPACE, xmlns: XMLNS_NAMESPACE }

  // http://www.w3.org/TR/REC-xml/#NT-NameStartChar
  // This implementation works on strings, a single character at a time
  // as such, it cannot ever support astral-plane characters (10000-EFFFF)
  // without a significant breaking change to either this  parser, or the
  // JavaScript language.  Implementation of an emoji-capable xml parser
  // is left as an exercise for the reader.
  var nameStart = /[:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]/

  var nameBody = /[:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\u00B7\u0300-\u036F\u203F-\u2040.\d-]/

  var entityStart = /[#:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]/
  var entityBody = /[#:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\u00B7\u0300-\u036F\u203F-\u2040.\d-]/

  function isWhitespace (c) {
    return c === ' ' || c === '\n' || c === '\r' || c === '\t'
  }

  function isQuote (c) {
    return c === '"' || c === '\''
  }

  function isAttribEnd (c) {
    return c === '>' || isWhitespace(c)
  }

  function isMatch (regex, c) {
    return regex.test(c)
  }

  function notMatch (regex, c) {
    return !isMatch(regex, c)
  }

  var S = 0
  sax.STATE = {
    BEGIN: S++, // leading byte order mark or whitespace
    BEGIN_WHITESPACE: S++, // leading whitespace
    TEXT: S++, // general stuff
    TEXT_ENTITY: S++, // &amp and such.
    OPEN_WAKA: S++, // <
    SGML_DECL: S++, // <!BLARG
    SGML_DECL_QUOTED: S++, // <!BLARG foo "bar
    DOCTYPE: S++, // <!DOCTYPE
    DOCTYPE_QUOTED: S++, // <!DOCTYPE "//blah
    DOCTYPE_DTD: S++, // <!DOCTYPE "//blah" [ ...
    DOCTYPE_DTD_QUOTED: S++, // <!DOCTYPE "//blah" [ "foo
    COMMENT_STARTING: S++, // <!-
    COMMENT: S++, // <!--
    COMMENT_ENDING: S++, // <!-- blah -
    COMMENT_ENDED: S++, // <!-- blah --
    CDATA: S++, // <![CDATA[ something
    CDATA_ENDING: S++, // ]
    CDATA_ENDING_2: S++, // ]]
    PROC_INST: S++, // <?hi
    PROC_INST_BODY: S++, // <?hi there
    PROC_INST_ENDING: S++, // <?hi "there" ?
    OPEN_TAG: S++, // <strong
    OPEN_TAG_SLASH: S++, // <strong /
    ATTRIB: S++, // <a
    ATTRIB_NAME: S++, // <a foo
    ATTRIB_NAME_SAW_WHITE: S++, // <a foo _
    ATTRIB_VALUE: S++, // <a foo=
    ATTRIB_VALUE_QUOTED: S++, // <a foo="bar
    ATTRIB_VALUE_CLOSED: S++, // <a foo="bar"
    ATTRIB_VALUE_UNQUOTED: S++, // <a foo=bar
    ATTRIB_VALUE_ENTITY_Q: S++, // <foo bar="&quot;"
    ATTRIB_VALUE_ENTITY_U: S++, // <foo bar=&quot
    CLOSE_TAG: S++, // </a
    CLOSE_TAG_SAW_WHITE: S++, // </a   >
    SCRIPT: S++, // <script> ...
    SCRIPT_ENDING: S++ // <script> ... <
  }

  sax.XML_ENTITIES = {
    'amp': '&',
    'gt': '>',
    'lt': '<',
    'quot': '"',
    'apos': "'"
  }

  sax.ENTITIES = {
    'amp': '&',
    'gt': '>',
    'lt': '<',
    'quot': '"',
    'apos': "'",
    'AElig': 198,
    'Aacute': 193,
    'Acirc': 194,
    'Agrave': 192,
    'Aring': 197,
    'Atilde': 195,
    'Auml': 196,
    'Ccedil': 199,
    'ETH': 208,
    'Eacute': 201,
    'Ecirc': 202,
    'Egrave': 200,
    'Euml': 203,
    'Iacute': 205,
    'Icirc': 206,
    'Igrave': 204,
    'Iuml': 207,
    'Ntilde': 209,
    'Oacute': 211,
    'Ocirc': 212,
    'Ograve': 210,
    'Oslash': 216,
    'Otilde': 213,
    'Ouml': 214,
    'THORN': 222,
    'Uacute': 218,
    'Ucirc': 219,
    'Ugrave': 217,
    'Uuml': 220,
    'Yacute': 221,
    'aacute': 225,
    'acirc': 226,
    'aelig': 230,
    'agrave': 224,
    'aring': 229,
    'atilde': 227,
    'auml': 228,
    'ccedil': 231,
    'eacute': 233,
    'ecirc': 234,
    'egrave': 232,
    'eth': 240,
    'euml': 235,
    'iacute': 237,
    'icirc': 238,
    'igrave': 236,
    'iuml': 239,
    'ntilde': 241,
    'oacute': 243,
    'ocirc': 244,
    'ograve': 242,
    'oslash': 248,
    'otilde': 245,
    'ouml': 246,
    'szlig': 223,
    'thorn': 254,
    'uacute': 250,
    'ucirc': 251,
    'ugrave': 249,
    'uuml': 252,
    'yacute': 253,
    'yuml': 255,
    'copy': 169,
    'reg': 174,
    'nbsp': 160,
    'iexcl': 161,
    'cent': 162,
    'pound': 163,
    'curren': 164,
    'yen': 165,
    'brvbar': 166,
    'sect': 167,
    'uml': 168,
    'ordf': 170,
    'laquo': 171,
    'not': 172,
    'shy': 173,
    'macr': 175,
    'deg': 176,
    'plusmn': 177,
    'sup1': 185,
    'sup2': 178,
    'sup3': 179,
    'acute': 180,
    'micro': 181,
    'para': 182,
    'middot': 183,
    'cedil': 184,
    'ordm': 186,
    'raquo': 187,
    'frac14': 188,
    'frac12': 189,
    'frac34': 190,
    'iquest': 191,
    'times': 215,
    'divide': 247,
    'OElig': 338,
    'oelig': 339,
    'Scaron': 352,
    'scaron': 353,
    'Yuml': 376,
    'fnof': 402,
    'circ': 710,
    'tilde': 732,
    'Alpha': 913,
    'Beta': 914,
    'Gamma': 915,
    'Delta': 916,
    'Epsilon': 917,
    'Zeta': 918,
    'Eta': 919,
    'Theta': 920,
    'Iota': 921,
    'Kappa': 922,
    'Lambda': 923,
    'Mu': 924,
    'Nu': 925,
    'Xi': 926,
    'Omicron': 927,
    'Pi': 928,
    'Rho': 929,
    'Sigma': 931,
    'Tau': 932,
    'Upsilon': 933,
    'Phi': 934,
    'Chi': 935,
    'Psi': 936,
    'Omega': 937,
    'alpha': 945,
    'beta': 946,
    'gamma': 947,
    'delta': 948,
    'epsilon': 949,
    'zeta': 950,
    'eta': 951,
    'theta': 952,
    'iota': 953,
    'kappa': 954,
    'lambda': 955,
    'mu': 956,
    'nu': 957,
    'xi': 958,
    'omicron': 959,
    'pi': 960,
    'rho': 961,
    'sigmaf': 962,
    'sigma': 963,
    'tau': 964,
    'upsilon': 965,
    'phi': 966,
    'chi': 967,
    'psi': 968,
    'omega': 969,
    'thetasym': 977,
    'upsih': 978,
    'piv': 982,
    'ensp': 8194,
    'emsp': 8195,
    'thinsp': 8201,
    'zwnj': 8204,
    'zwj': 8205,
    'lrm': 8206,
    'rlm': 8207,
    'ndash': 8211,
    'mdash': 8212,
    'lsquo': 8216,
    'rsquo': 8217,
    'sbquo': 8218,
    'ldquo': 8220,
    'rdquo': 8221,
    'bdquo': 8222,
    'dagger': 8224,
    'Dagger': 8225,
    'bull': 8226,
    'hellip': 8230,
    'permil': 8240,
    'prime': 8242,
    'Prime': 8243,
    'lsaquo': 8249,
    'rsaquo': 8250,
    'oline': 8254,
    'frasl': 8260,
    'euro': 8364,
    'image': 8465,
    'weierp': 8472,
    'real': 8476,
    'trade': 8482,
    'alefsym': 8501,
    'larr': 8592,
    'uarr': 8593,
    'rarr': 8594,
    'darr': 8595,
    'harr': 8596,
    'crarr': 8629,
    'lArr': 8656,
    'uArr': 8657,
    'rArr': 8658,
    'dArr': 8659,
    'hArr': 8660,
    'forall': 8704,
    'part': 8706,
    'exist': 8707,
    'empty': 8709,
    'nabla': 8711,
    'isin': 8712,
    'notin': 8713,
    'ni': 8715,
    'prod': 8719,
    'sum': 8721,
    'minus': 8722,
    'lowast': 8727,
    'radic': 8730,
    'prop': 8733,
    'infin': 8734,
    'ang': 8736,
    'and': 8743,
    'or': 8744,
    'cap': 8745,
    'cup': 8746,
    'int': 8747,
    'there4': 8756,
    'sim': 8764,
    'cong': 8773,
    'asymp': 8776,
    'ne': 8800,
    'equiv': 8801,
    'le': 8804,
    'ge': 8805,
    'sub': 8834,
    'sup': 8835,
    'nsub': 8836,
    'sube': 8838,
    'supe': 8839,
    'oplus': 8853,
    'otimes': 8855,
    'perp': 8869,
    'sdot': 8901,
    'lceil': 8968,
    'rceil': 8969,
    'lfloor': 8970,
    'rfloor': 8971,
    'lang': 9001,
    'rang': 9002,
    'loz': 9674,
    'spades': 9824,
    'clubs': 9827,
    'hearts': 9829,
    'diams': 9830
  }

  Object.keys(sax.ENTITIES).forEach(function (key) {
    var e = sax.ENTITIES[key]
    var s = typeof e === 'number' ? String.fromCharCode(e) : e
    sax.ENTITIES[key] = s
  })

  for (var s in sax.STATE) {
    sax.STATE[sax.STATE[s]] = s
  }

  // shorthand
  S = sax.STATE

  function emit (parser, event, data) {
    parser[event] && parser[event](data)
  }

  function emitNode (parser, nodeType, data) {
    if (parser.textNode) closeText(parser)
    emit(parser, nodeType, data)
  }

  function closeText (parser) {
    parser.textNode = textopts(parser.opt, parser.textNode)
    if (parser.textNode) emit(parser, 'ontext', parser.textNode)
    parser.textNode = ''
  }

  function textopts (opt, text) {
    if (opt.trim) text = text.trim()
    if (opt.normalize) text = text.replace(/\s+/g, ' ')
    return text
  }

  function error (parser, er) {
    closeText(parser)
    if (parser.trackPosition) {
      er += '\nLine: ' + parser.line +
        '\nColumn: ' + parser.column +
        '\nChar: ' + parser.c
    }
    er = new Error(er)
    parser.error = er
    emit(parser, 'onerror', er)
    return parser
  }

  function end (parser) {
    if (parser.sawRoot && !parser.closedRoot) strictFail(parser, 'Unclosed root tag')
    if ((parser.state !== S.BEGIN) &&
      (parser.state !== S.BEGIN_WHITESPACE) &&
      (parser.state !== S.TEXT)) {
      error(parser, 'Unexpected end')
    }
    closeText(parser)
    parser.c = ''
    parser.closed = true
    emit(parser, 'onend')
    SAXParser.call(parser, parser.strict, parser.opt)
    return parser
  }

  function strictFail (parser, message) {
    if (typeof parser !== 'object' || !(parser instanceof SAXParser)) {
      throw new Error('bad call to strictFail')
    }
    if (parser.strict) {
      error(parser, message)
    }
  }

  function newTag (parser) {
    if (!parser.strict) parser.tagName = parser.tagName[parser.looseCase]()
    var parent = parser.tags[parser.tags.length - 1] || parser
    var tag = parser.tag = { name: parser.tagName, attributes: {} }

    // will be overridden if tag contails an xmlns="foo" or xmlns:foo="bar"
    if (parser.opt.xmlns) {
      tag.ns = parent.ns
    }
    parser.attribList.length = 0
    emitNode(parser, 'onopentagstart', tag)
  }

  function qname (name, attribute) {
    var i = name.indexOf(':')
    var qualName = i < 0 ? [ '', name ] : name.split(':')
    var prefix = qualName[0]
    var local = qualName[1]

    // <x "xmlns"="http://foo">
    if (attribute && name === 'xmlns') {
      prefix = 'xmlns'
      local = ''
    }

    return { prefix: prefix, local: local }
  }

  function attrib (parser) {
    if (!parser.strict) {
      parser.attribName = parser.attribName[parser.looseCase]()
    }

    if (parser.attribList.indexOf(parser.attribName) !== -1 ||
      parser.tag.attributes.hasOwnProperty(parser.attribName)) {
      parser.attribName = parser.attribValue = ''
      return
    }

    if (parser.opt.xmlns) {
      var qn = qname(parser.attribName, true)
      var prefix = qn.prefix
      var local = qn.local

      if (prefix === 'xmlns') {
        // namespace binding attribute. push the binding into scope
        if (local === 'xml' && parser.attribValue !== XML_NAMESPACE) {
          strictFail(parser,
            'xml: prefix must be bound to ' + XML_NAMESPACE + '\n' +
            'Actual: ' + parser.attribValue)
        } else if (local === 'xmlns' && parser.attribValue !== XMLNS_NAMESPACE) {
          strictFail(parser,
            'xmlns: prefix must be bound to ' + XMLNS_NAMESPACE + '\n' +
            'Actual: ' + parser.attribValue)
        } else {
          var tag = parser.tag
          var parent = parser.tags[parser.tags.length - 1] || parser
          if (tag.ns === parent.ns) {
            tag.ns = Object.create(parent.ns)
          }
          tag.ns[local] = parser.attribValue
        }
      }

      // defer onattribute events until all attributes have been seen
      // so any new bindings can take effect. preserve attribute order
      // so deferred events can be emitted in document order
      parser.attribList.push([parser.attribName, parser.attribValue])
    } else {
      // in non-xmlns mode, we can emit the event right away
      parser.tag.attributes[parser.attribName] = parser.attribValue
      emitNode(parser, 'onattribute', {
        name: parser.attribName,
        value: parser.attribValue
      })
    }

    parser.attribName = parser.attribValue = ''
  }

  function openTag (parser, selfClosing) {
    if (parser.opt.xmlns) {
      // emit namespace binding events
      var tag = parser.tag

      // add namespace info to tag
      var qn = qname(parser.tagName)
      tag.prefix = qn.prefix
      tag.local = qn.local
      tag.uri = tag.ns[qn.prefix] || ''

      if (tag.prefix && !tag.uri) {
        strictFail(parser, 'Unbound namespace prefix: ' +
          JSON.stringify(parser.tagName))
        tag.uri = qn.prefix
      }

      var parent = parser.tags[parser.tags.length - 1] || parser
      if (tag.ns && parent.ns !== tag.ns) {
        Object.keys(tag.ns).forEach(function (p) {
          emitNode(parser, 'onopennamespace', {
            prefix: p,
            uri: tag.ns[p]
          })
        })
      }

      // handle deferred onattribute events
      // Note: do not apply default ns to attributes:
      //   http://www.w3.org/TR/REC-xml-names/#defaulting
      for (var i = 0, l = parser.attribList.length; i < l; i++) {
        var nv = parser.attribList[i]
        var name = nv[0]
        var value = nv[1]
        var qualName = qname(name, true)
        var prefix = qualName.prefix
        var local = qualName.local
        var uri = prefix === '' ? '' : (tag.ns[prefix] || '')
        var a = {
          name: name,
          value: value,
          prefix: prefix,
          local: local,
          uri: uri
        }

        // if there's any attributes with an undefined namespace,
        // then fail on them now.
        if (prefix && prefix !== 'xmlns' && !uri) {
          strictFail(parser, 'Unbound namespace prefix: ' +
            JSON.stringify(prefix))
          a.uri = prefix
        }
        parser.tag.attributes[name] = a
        emitNode(parser, 'onattribute', a)
      }
      parser.attribList.length = 0
    }

    parser.tag.isSelfClosing = !!selfClosing

    // process the tag
    parser.sawRoot = true
    parser.tags.push(parser.tag)
    emitNode(parser, 'onopentag', parser.tag)
    if (!selfClosing) {
      // special case for <script> in non-strict mode.
      if (!parser.noscript && parser.tagName.toLowerCase() === 'script') {
        parser.state = S.SCRIPT
      } else {
        parser.state = S.TEXT
      }
      parser.tag = null
      parser.tagName = ''
    }
    parser.attribName = parser.attribValue = ''
    parser.attribList.length = 0
  }

  function closeTag (parser) {
    if (!parser.tagName) {
      strictFail(parser, 'Weird empty close tag.')
      parser.textNode += '</>'
      parser.state = S.TEXT
      return
    }

    if (parser.script) {
      if (parser.tagName !== 'script') {
        parser.script += '</' + parser.tagName + '>'
        parser.tagName = ''
        parser.state = S.SCRIPT
        return
      }
      emitNode(parser, 'onscript', parser.script)
      parser.script = ''
    }

    // first make sure that the closing tag actually exists.
    // <a><b></c></b></a> will close everything, otherwise.
    var t = parser.tags.length
    var tagName = parser.tagName
    if (!parser.strict) {
      tagName = tagName[parser.looseCase]()
    }
    var closeTo = tagName
    while (t--) {
      var close = parser.tags[t]
      if (close.name !== closeTo) {
        // fail the first time in strict mode
        strictFail(parser, 'Unexpected close tag')
      } else {
        break
      }
    }

    // didn't find it.  we already failed for strict, so just abort.
    if (t < 0) {
      strictFail(parser, 'Unmatched closing tag: ' + parser.tagName)
      parser.textNode += '</' + parser.tagName + '>'
      parser.state = S.TEXT
      return
    }
    parser.tagName = tagName
    var s = parser.tags.length
    while (s-- > t) {
      var tag = parser.tag = parser.tags.pop()
      parser.tagName = parser.tag.name
      emitNode(parser, 'onclosetag', parser.tagName)

      var x = {}
      for (var i in tag.ns) {
        x[i] = tag.ns[i]
      }

      var parent = parser.tags[parser.tags.length - 1] || parser
      if (parser.opt.xmlns && tag.ns !== parent.ns) {
        // remove namespace bindings introduced by tag
        Object.keys(tag.ns).forEach(function (p) {
          var n = tag.ns[p]
          emitNode(parser, 'onclosenamespace', { prefix: p, uri: n })
        })
      }
    }
    if (t === 0) parser.closedRoot = true
    parser.tagName = parser.attribValue = parser.attribName = ''
    parser.attribList.length = 0
    parser.state = S.TEXT
  }

  function parseEntity (parser) {
    var entity = parser.entity
    var entityLC = entity.toLowerCase()
    var num
    var numStr = ''

    if (parser.ENTITIES[entity]) {
      return parser.ENTITIES[entity]
    }
    if (parser.ENTITIES[entityLC]) {
      return parser.ENTITIES[entityLC]
    }
    entity = entityLC
    if (entity.charAt(0) === '#') {
      if (entity.charAt(1) === 'x') {
        entity = entity.slice(2)
        num = parseInt(entity, 16)
        numStr = num.toString(16)
      } else {
        entity = entity.slice(1)
        num = parseInt(entity, 10)
        numStr = num.toString(10)
      }
    }
    entity = entity.replace(/^0+/, '')
    if (isNaN(num) || numStr.toLowerCase() !== entity) {
      strictFail(parser, 'Invalid character entity')
      return '&' + parser.entity + ';'
    }

    return String.fromCodePoint(num)
  }

  function beginWhiteSpace (parser, c) {
    if (c === '<') {
      parser.state = S.OPEN_WAKA
      parser.startTagPosition = parser.position
    } else if (!isWhitespace(c)) {
      // have to process this as a text node.
      // weird, but happens.
      strictFail(parser, 'Non-whitespace before first tag.')
      parser.textNode = c
      parser.state = S.TEXT
    }
  }

  function charAt (chunk, i) {
    var result = ''
    if (i < chunk.length) {
      result = chunk.charAt(i)
    }
    return result
  }

  function write (chunk) {
    var parser = this
    if (this.error) {
      throw this.error
    }
    if (parser.closed) {
      return error(parser,
        'Cannot write after close. Assign an onready handler.')
    }
    if (chunk === null) {
      return end(parser)
    }
    if (typeof chunk === 'object') {
      chunk = chunk.toString()
    }
    var i = 0
    var c = ''
    while (true) {
      c = charAt(chunk, i++)
      parser.c = c

      if (!c) {
        break
      }

      if (parser.trackPosition) {
        parser.position++
        if (c === '\n') {
          parser.line++
          parser.column = 0
        } else {
          parser.column++
        }
      }

      switch (parser.state) {
        case S.BEGIN:
          parser.state = S.BEGIN_WHITESPACE
          if (c === '\uFEFF') {
            continue
          }
          beginWhiteSpace(parser, c)
          continue

        case S.BEGIN_WHITESPACE:
          beginWhiteSpace(parser, c)
          continue

        case S.TEXT:
          if (parser.sawRoot && !parser.closedRoot) {
            var starti = i - 1
            while (c && c !== '<' && c !== '&') {
              c = charAt(chunk, i++)
              if (c && parser.trackPosition) {
                parser.position++
                if (c === '\n') {
                  parser.line++
                  parser.column = 0
                } else {
                  parser.column++
                }
              }
            }
            parser.textNode += chunk.substring(starti, i - 1)
          }
          if (c === '<' && !(parser.sawRoot && parser.closedRoot && !parser.strict)) {
            parser.state = S.OPEN_WAKA
            parser.startTagPosition = parser.position
          } else {
            if (!isWhitespace(c) && (!parser.sawRoot || parser.closedRoot)) {
              strictFail(parser, 'Text data outside of root node.')
            }
            if (c === '&') {
              parser.state = S.TEXT_ENTITY
            } else {
              parser.textNode += c
            }
          }
          continue

        case S.SCRIPT:
          // only non-strict
          if (c === '<') {
            parser.state = S.SCRIPT_ENDING
          } else {
            parser.script += c
          }
          continue

        case S.SCRIPT_ENDING:
          if (c === '/') {
            parser.state = S.CLOSE_TAG
          } else {
            parser.script += '<' + c
            parser.state = S.SCRIPT
          }
          continue

        case S.OPEN_WAKA:
          // either a /, ?, !, or text is coming next.
          if (c === '!') {
            parser.state = S.SGML_DECL
            parser.sgmlDecl = ''
          } else if (isWhitespace(c)) {
            // wait for it...
          } else if (isMatch(nameStart, c)) {
            parser.state = S.OPEN_TAG
            parser.tagName = c
          } else if (c === '/') {
            parser.state = S.CLOSE_TAG
            parser.tagName = ''
          } else if (c === '?') {
            parser.state = S.PROC_INST
            parser.procInstName = parser.procInstBody = ''
          } else {
            strictFail(parser, 'Unencoded <')
            // if there was some whitespace, then add that in.
            if (parser.startTagPosition + 1 < parser.position) {
              var pad = parser.position - parser.startTagPosition
              c = new Array(pad).join(' ') + c
            }
            parser.textNode += '<' + c
            parser.state = S.TEXT
          }
          continue

        case S.SGML_DECL:
          if ((parser.sgmlDecl + c).toUpperCase() === CDATA) {
            emitNode(parser, 'onopencdata')
            parser.state = S.CDATA
            parser.sgmlDecl = ''
            parser.cdata = ''
          } else if (parser.sgmlDecl + c === '--') {
            parser.state = S.COMMENT
            parser.comment = ''
            parser.sgmlDecl = ''
          } else if ((parser.sgmlDecl + c).toUpperCase() === DOCTYPE) {
            parser.state = S.DOCTYPE
            if (parser.doctype || parser.sawRoot) {
              strictFail(parser,
                'Inappropriately located doctype declaration')
            }
            parser.doctype = ''
            parser.sgmlDecl = ''
          } else if (c === '>') {
            emitNode(parser, 'onsgmldeclaration', parser.sgmlDecl)
            parser.sgmlDecl = ''
            parser.state = S.TEXT
          } else if (isQuote(c)) {
            parser.state = S.SGML_DECL_QUOTED
            parser.sgmlDecl += c
          } else {
            parser.sgmlDecl += c
          }
          continue

        case S.SGML_DECL_QUOTED:
          if (c === parser.q) {
            parser.state = S.SGML_DECL
            parser.q = ''
          }
          parser.sgmlDecl += c
          continue

        case S.DOCTYPE:
          if (c === '>') {
            parser.state = S.TEXT
            emitNode(parser, 'ondoctype', parser.doctype)
            parser.doctype = true // just remember that we saw it.
          } else {
            parser.doctype += c
            if (c === '[') {
              parser.state = S.DOCTYPE_DTD
            } else if (isQuote(c)) {
              parser.state = S.DOCTYPE_QUOTED
              parser.q = c
            }
          }
          continue

        case S.DOCTYPE_QUOTED:
          parser.doctype += c
          if (c === parser.q) {
            parser.q = ''
            parser.state = S.DOCTYPE
          }
          continue

        case S.DOCTYPE_DTD:
          parser.doctype += c
          if (c === ']') {
            parser.state = S.DOCTYPE
          } else if (isQuote(c)) {
            parser.state = S.DOCTYPE_DTD_QUOTED
            parser.q = c
          }
          continue

        case S.DOCTYPE_DTD_QUOTED:
          parser.doctype += c
          if (c === parser.q) {
            parser.state = S.DOCTYPE_DTD
            parser.q = ''
          }
          continue

        case S.COMMENT:
          if (c === '-') {
            parser.state = S.COMMENT_ENDING
          } else {
            parser.comment += c
          }
          continue

        case S.COMMENT_ENDING:
          if (c === '-') {
            parser.state = S.COMMENT_ENDED
            parser.comment = textopts(parser.opt, parser.comment)
            if (parser.comment) {
              emitNode(parser, 'oncomment', parser.comment)
            }
            parser.comment = ''
          } else {
            parser.comment += '-' + c
            parser.state = S.COMMENT
          }
          continue

        case S.COMMENT_ENDED:
          if (c !== '>') {
            strictFail(parser, 'Malformed comment')
            // allow <!-- blah -- bloo --> in non-strict mode,
            // which is a comment of " blah -- bloo "
            parser.comment += '--' + c
            parser.state = S.COMMENT
          } else {
            parser.state = S.TEXT
          }
          continue

        case S.CDATA:
          if (c === ']') {
            parser.state = S.CDATA_ENDING
          } else {
            parser.cdata += c
          }
          continue

        case S.CDATA_ENDING:
          if (c === ']') {
            parser.state = S.CDATA_ENDING_2
          } else {
            parser.cdata += ']' + c
            parser.state = S.CDATA
          }
          continue

        case S.CDATA_ENDING_2:
          if (c === '>') {
            if (parser.cdata) {
              emitNode(parser, 'oncdata', parser.cdata)
            }
            emitNode(parser, 'onclosecdata')
            parser.cdata = ''
            parser.state = S.TEXT
          } else if (c === ']') {
            parser.cdata += ']'
          } else {
            parser.cdata += ']]' + c
            parser.state = S.CDATA
          }
          continue

        case S.PROC_INST:
          if (c === '?') {
            parser.state = S.PROC_INST_ENDING
          } else if (isWhitespace(c)) {
            parser.state = S.PROC_INST_BODY
          } else {
            parser.procInstName += c
          }
          continue

        case S.PROC_INST_BODY:
          if (!parser.procInstBody && isWhitespace(c)) {
            continue
          } else if (c === '?') {
            parser.state = S.PROC_INST_ENDING
          } else {
            parser.procInstBody += c
          }
          continue

        case S.PROC_INST_ENDING:
          if (c === '>') {
            emitNode(parser, 'onprocessinginstruction', {
              name: parser.procInstName,
              body: parser.procInstBody
            })
            parser.procInstName = parser.procInstBody = ''
            parser.state = S.TEXT
          } else {
            parser.procInstBody += '?' + c
            parser.state = S.PROC_INST_BODY
          }
          continue

        case S.OPEN_TAG:
          if (isMatch(nameBody, c)) {
            parser.tagName += c
          } else {
            newTag(parser)
            if (c === '>') {
              openTag(parser)
            } else if (c === '/') {
              parser.state = S.OPEN_TAG_SLASH
            } else {
              if (!isWhitespace(c)) {
                strictFail(parser, 'Invalid character in tag name')
              }
              parser.state = S.ATTRIB
            }
          }
          continue

        case S.OPEN_TAG_SLASH:
          if (c === '>') {
            openTag(parser, true)
            closeTag(parser)
          } else {
            strictFail(parser, 'Forward-slash in opening tag not followed by >')
            parser.state = S.ATTRIB
          }
          continue

        case S.ATTRIB:
          // haven't read the attribute name yet.
          if (isWhitespace(c)) {
            continue
          } else if (c === '>') {
            openTag(parser)
          } else if (c === '/') {
            parser.state = S.OPEN_TAG_SLASH
          } else if (isMatch(nameStart, c)) {
            parser.attribName = c
            parser.attribValue = ''
            parser.state = S.ATTRIB_NAME
          } else {
            strictFail(parser, 'Invalid attribute name')
          }
          continue

        case S.ATTRIB_NAME:
          if (c === '=') {
            parser.state = S.ATTRIB_VALUE
          } else if (c === '>') {
            strictFail(parser, 'Attribute without value')
            parser.attribValue = parser.attribName
            attrib(parser)
            openTag(parser)
          } else if (isWhitespace(c)) {
            parser.state = S.ATTRIB_NAME_SAW_WHITE
          } else if (isMatch(nameBody, c)) {
            parser.attribName += c
          } else {
            strictFail(parser, 'Invalid attribute name')
          }
          continue

        case S.ATTRIB_NAME_SAW_WHITE:
          if (c === '=') {
            parser.state = S.ATTRIB_VALUE
          } else if (isWhitespace(c)) {
            continue
          } else {
            strictFail(parser, 'Attribute without value')
            parser.tag.attributes[parser.attribName] = ''
            parser.attribValue = ''
            emitNode(parser, 'onattribute', {
              name: parser.attribName,
              value: ''
            })
            parser.attribName = ''
            if (c === '>') {
              openTag(parser)
            } else if (isMatch(nameStart, c)) {
              parser.attribName = c
              parser.state = S.ATTRIB_NAME
            } else {
              strictFail(parser, 'Invalid attribute name')
              parser.state = S.ATTRIB
            }
          }
          continue

        case S.ATTRIB_VALUE:
          if (isWhitespace(c)) {
            continue
          } else if (isQuote(c)) {
            parser.q = c
            parser.state = S.ATTRIB_VALUE_QUOTED
          } else {
            strictFail(parser, 'Unquoted attribute value')
            parser.state = S.ATTRIB_VALUE_UNQUOTED
            parser.attribValue = c
          }
          continue

        case S.ATTRIB_VALUE_QUOTED:
          if (c !== parser.q) {
            if (c === '&') {
              parser.state = S.ATTRIB_VALUE_ENTITY_Q
            } else {
              parser.attribValue += c
            }
            continue
          }
          attrib(parser)
          parser.q = ''
          parser.state = S.ATTRIB_VALUE_CLOSED
          continue

        case S.ATTRIB_VALUE_CLOSED:
          if (isWhitespace(c)) {
            parser.state = S.ATTRIB
          } else if (c === '>') {
            openTag(parser)
          } else if (c === '/') {
            parser.state = S.OPEN_TAG_SLASH
          } else if (isMatch(nameStart, c)) {
            strictFail(parser, 'No whitespace between attributes')
            parser.attribName = c
            parser.attribValue = ''
            parser.state = S.ATTRIB_NAME
          } else {
            strictFail(parser, 'Invalid attribute name')
          }
          continue

        case S.ATTRIB_VALUE_UNQUOTED:
          if (!isAttribEnd(c)) {
            if (c === '&') {
              parser.state = S.ATTRIB_VALUE_ENTITY_U
            } else {
              parser.attribValue += c
            }
            continue
          }
          attrib(parser)
          if (c === '>') {
            openTag(parser)
          } else {
            parser.state = S.ATTRIB
          }
          continue

        case S.CLOSE_TAG:
          if (!parser.tagName) {
            if (isWhitespace(c)) {
              continue
            } else if (notMatch(nameStart, c)) {
              if (parser.script) {
                parser.script += '</' + c
                parser.state = S.SCRIPT
              } else {
                strictFail(parser, 'Invalid tagname in closing tag.')
              }
            } else {
              parser.tagName = c
            }
          } else if (c === '>') {
            closeTag(parser)
          } else if (isMatch(nameBody, c)) {
            parser.tagName += c
          } else if (parser.script) {
            parser.script += '</' + parser.tagName
            parser.tagName = ''
            parser.state = S.SCRIPT
          } else {
            if (!isWhitespace(c)) {
              strictFail(parser, 'Invalid tagname in closing tag')
            }
            parser.state = S.CLOSE_TAG_SAW_WHITE
          }
          continue

        case S.CLOSE_TAG_SAW_WHITE:
          if (isWhitespace(c)) {
            continue
          }
          if (c === '>') {
            closeTag(parser)
          } else {
            strictFail(parser, 'Invalid characters in closing tag')
          }
          continue

        case S.TEXT_ENTITY:
        case S.ATTRIB_VALUE_ENTITY_Q:
        case S.ATTRIB_VALUE_ENTITY_U:
          var returnState
          var buffer
          switch (parser.state) {
            case S.TEXT_ENTITY:
              returnState = S.TEXT
              buffer = 'textNode'
              break

            case S.ATTRIB_VALUE_ENTITY_Q:
              returnState = S.ATTRIB_VALUE_QUOTED
              buffer = 'attribValue'
              break

            case S.ATTRIB_VALUE_ENTITY_U:
              returnState = S.ATTRIB_VALUE_UNQUOTED
              buffer = 'attribValue'
              break
          }

          if (c === ';') {
            parser[buffer] += parseEntity(parser)
            parser.entity = ''
            parser.state = returnState
          } else if (isMatch(parser.entity.length ? entityBody : entityStart, c)) {
            parser.entity += c
          } else {
            strictFail(parser, 'Invalid character in entity name')
            parser[buffer] += '&' + parser.entity + c
            parser.entity = ''
            parser.state = returnState
          }

          continue

        default:
          throw new Error(parser, 'Unknown state: ' + parser.state)
      }
    } // while

    if (parser.position >= parser.bufferCheckPosition) {
      checkBufferLength(parser)
    }
    return parser
  }

  /*! http://mths.be/fromcodepoint v0.1.0 by @mathias */
  /* istanbul ignore next */
  if (!String.fromCodePoint) {
    (function () {
      var stringFromCharCode = String.fromCharCode
      var floor = Math.floor
      var fromCodePoint = function () {
        var MAX_SIZE = 0x4000
        var codeUnits = []
        var highSurrogate
        var lowSurrogate
        var index = -1
        var length = arguments.length
        if (!length) {
          return ''
        }
        var result = ''
        while (++index < length) {
          var codePoint = Number(arguments[index])
          if (
            !isFinite(codePoint) || // `NaN`, `+Infinity`, or `-Infinity`
            codePoint < 0 || // not a valid Unicode code point
            codePoint > 0x10FFFF || // not a valid Unicode code point
            floor(codePoint) !== codePoint // not an integer
          ) {
            throw RangeError('Invalid code point: ' + codePoint)
          }
          if (codePoint <= 0xFFFF) { // BMP code point
            codeUnits.push(codePoint)
          } else { // Astral code point; split in surrogate halves
            // http://mathiasbynens.be/notes/javascript-encoding#surrogate-formulae
            codePoint -= 0x10000
            highSurrogate = (codePoint >> 10) + 0xD800
            lowSurrogate = (codePoint % 0x400) + 0xDC00
            codeUnits.push(highSurrogate, lowSurrogate)
          }
          if (index + 1 === length || codeUnits.length > MAX_SIZE) {
            result += stringFromCharCode.apply(null, codeUnits)
            codeUnits.length = 0
          }
        }
        return result
      }
      /* istanbul ignore next */
      if (Object.defineProperty) {
        Object.defineProperty(String, 'fromCodePoint', {
          value: fromCodePoint,
          configurable: true,
          writable: true
        })
      } else {
        String.fromCodePoint = fromCodePoint
      }
    }())
  }
})( false ? undefined : exports)

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(10).Buffer))

/***/ }),
/* 546 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


var isArray = __webpack_require__(0).isArray;

function groupDecorations(line) {
	var groups = [], currentGroup = null;
	for (var i = 0, l = line.inlines.length; i < l; i++) {
		var inline = line.inlines[i];
		var decoration = inline.decoration;
		if (!decoration) {
			currentGroup = null;
			continue;
		}
		if (!isArray(decoration)) {
			decoration = [decoration];
		}
		var color = inline.decorationColor || inline.color || 'black';
		var style = inline.decorationStyle || 'solid';
		for (var ii = 0, ll = decoration.length; ii < ll; ii++) {
			var decorationItem = decoration[ii];
			if (!currentGroup || decorationItem !== currentGroup.decoration ||
				style !== currentGroup.decorationStyle || color !== currentGroup.decorationColor) {

				currentGroup = {
					line: line,
					decoration: decorationItem,
					decorationColor: color,
					decorationStyle: style,
					inlines: [inline]
				};
				groups.push(currentGroup);
			} else {
				currentGroup.inlines.push(inline);
			}
		}
	}

	return groups;
}

function drawDecoration(group, x, y, pdfKitDoc) {
	function maxInline() {
		var max = 0;
		for (var i = 0, l = group.inlines.length; i < l; i++) {
			var inline = group.inlines[i];
			max = inline.fontSize > max ? i : max;
		}
		return group.inlines[max];
	}
	function width() {
		var sum = 0;
		for (var i = 0, l = group.inlines.length; i < l; i++) {
			var justifyShift = (group.inlines[i].justifyShift || 0);
			sum += group.inlines[i].width + justifyShift;
		}
		return sum;
	}
	var firstInline = group.inlines[0],
		biggerInline = maxInline(),
		totalWidth = width(),
		lineAscent = group.line.getAscenderHeight(),
		ascent = biggerInline.font.ascender / 1000 * biggerInline.fontSize,
		height = biggerInline.height,
		descent = height - ascent;

	var lw = 0.5 + Math.floor(Math.max(biggerInline.fontSize - 8, 0) / 2) * 0.12;

	switch (group.decoration) {
		case 'underline':
			y += lineAscent + descent * 0.45;
			break;
		case 'overline':
			y += lineAscent - (ascent * 0.85);
			break;
		case 'lineThrough':
			y += lineAscent - (ascent * 0.25);
			break;
		default:
			throw 'Unkown decoration : ' + group.decoration;
	}
	pdfKitDoc.save();

	if (group.decorationStyle === 'double') {
		var gap = Math.max(0.5, lw * 2);
		pdfKitDoc.fillColor(group.decorationColor)
			.rect(x + firstInline.x, y - lw / 2, totalWidth, lw / 2).fill()
			.rect(x + firstInline.x, y + gap - lw / 2, totalWidth, lw / 2).fill();
	} else if (group.decorationStyle === 'dashed') {
		var nbDashes = Math.ceil(totalWidth / (3.96 + 2.84));
		var rdx = x + firstInline.x;
		pdfKitDoc.rect(rdx, y, totalWidth, lw).clip();
		pdfKitDoc.fillColor(group.decorationColor);
		for (var i = 0; i < nbDashes; i++) {
			pdfKitDoc.rect(rdx, y - lw / 2, 3.96, lw).fill();
			rdx += 3.96 + 2.84;
		}
	} else if (group.decorationStyle === 'dotted') {
		var nbDots = Math.ceil(totalWidth / (lw * 3));
		var rx = x + firstInline.x;
		pdfKitDoc.rect(rx, y, totalWidth, lw).clip();
		pdfKitDoc.fillColor(group.decorationColor);
		for (var ii = 0; ii < nbDots; ii++) {
			pdfKitDoc.rect(rx, y - lw / 2, lw, lw).fill();
			rx += (lw * 3);
		}
	} else if (group.decorationStyle === 'wavy') {
		var sh = 0.7, sv = 1;
		var nbWaves = Math.ceil(totalWidth / (sh * 2)) + 1;
		var rwx = x + firstInline.x - 1;
		pdfKitDoc.rect(x + firstInline.x, y - sv, totalWidth, y + sv).clip();
		pdfKitDoc.lineWidth(0.24);
		pdfKitDoc.moveTo(rwx, y);
		for (var iii = 0; iii < nbWaves; iii++) {
			pdfKitDoc.bezierCurveTo(rwx + sh, y - sv, rwx + sh * 2, y - sv, rwx + sh * 3, y)
				.bezierCurveTo(rwx + sh * 4, y + sv, rwx + sh * 5, y + sv, rwx + sh * 6, y);
			rwx += sh * 6;
		}
		pdfKitDoc.stroke(group.decorationColor);
	} else {
		pdfKitDoc.fillColor(group.decorationColor)
			.rect(x + firstInline.x, y - lw / 2, totalWidth, lw)
			.fill();
	}
	pdfKitDoc.restore();
}

function drawDecorations(line, x, y, pdfKitDoc) {
	var groups = groupDecorations(line);
	for (var i = 0, l = groups.length; i < l; i++) {
		drawDecoration(groups[i], x, y, pdfKitDoc);
	}
}

function drawBackground(line, x, y, pdfKitDoc) {
	var height = line.getHeight();
	for (var i = 0, l = line.inlines.length; i < l; i++) {
		var inline = line.inlines[i];
		if (!inline.background) {
			continue;
		}
		var justifyShift = (inline.justifyShift || 0);
		pdfKitDoc.fillColor(inline.background)
			.rect(x + inline.x - justifyShift, y, inline.width + justifyShift, height)
			.fill();
	}
}

module.exports = {
	drawBackground: drawBackground,
	drawDecorations: drawDecorations
};


/***/ }),
/* 547 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(module) {

__webpack_require__(209);

__webpack_require__(211);

__webpack_require__(192);

__webpack_require__(28);

__webpack_require__(46);

__webpack_require__(157);

__webpack_require__(223);

__webpack_require__(227);

__webpack_require__(32);

__webpack_require__(118);

__webpack_require__(160);

__webpack_require__(230);

__webpack_require__(231);

__webpack_require__(232);

__webpack_require__(233);

__webpack_require__(120);

var SVGtoPDF = function SVGtoPDF(doc, svg, x, y, options) {
  "use strict";

  var NamedColors = {
    aliceblue: [240, 248, 255],
    antiquewhite: [250, 235, 215],
    aqua: [0, 255, 255],
    aquamarine: [127, 255, 212],
    azure: [240, 255, 255],
    beige: [245, 245, 220],
    bisque: [255, 228, 196],
    black: [0, 0, 0],
    blanchedalmond: [255, 235, 205],
    blue: [0, 0, 255],
    blueviolet: [138, 43, 226],
    brown: [165, 42, 42],
    burlywood: [222, 184, 135],
    cadetblue: [95, 158, 160],
    chartreuse: [127, 255, 0],
    chocolate: [210, 105, 30],
    coral: [255, 127, 80],
    cornflowerblue: [100, 149, 237],
    cornsilk: [255, 248, 220],
    crimson: [220, 20, 60],
    cyan: [0, 255, 255],
    darkblue: [0, 0, 139],
    darkcyan: [0, 139, 139],
    darkgoldenrod: [184, 134, 11],
    darkgray: [169, 169, 169],
    darkgrey: [169, 169, 169],
    darkgreen: [0, 100, 0],
    darkkhaki: [189, 183, 107],
    darkmagenta: [139, 0, 139],
    darkolivegreen: [85, 107, 47],
    darkorange: [255, 140, 0],
    darkorchid: [153, 50, 204],
    darkred: [139, 0, 0],
    darksalmon: [233, 150, 122],
    darkseagreen: [143, 188, 143],
    darkslateblue: [72, 61, 139],
    darkslategray: [47, 79, 79],
    darkslategrey: [47, 79, 79],
    darkturquoise: [0, 206, 209],
    darkviolet: [148, 0, 211],
    deeppink: [255, 20, 147],
    deepskyblue: [0, 191, 255],
    dimgray: [105, 105, 105],
    dimgrey: [105, 105, 105],
    dodgerblue: [30, 144, 255],
    firebrick: [178, 34, 34],
    floralwhite: [255, 250, 240],
    forestgreen: [34, 139, 34],
    fuchsia: [255, 0, 255],
    gainsboro: [220, 220, 220],
    ghostwhite: [248, 248, 255],
    gold: [255, 215, 0],
    goldenrod: [218, 165, 32],
    gray: [128, 128, 128],
    grey: [128, 128, 128],
    green: [0, 128, 0],
    greenyellow: [173, 255, 47],
    honeydew: [240, 255, 240],
    hotpink: [255, 105, 180],
    indianred: [205, 92, 92],
    indigo: [75, 0, 130],
    ivory: [255, 255, 240],
    khaki: [240, 230, 140],
    lavender: [230, 230, 250],
    lavenderblush: [255, 240, 245],
    lawngreen: [124, 252, 0],
    lemonchiffon: [255, 250, 205],
    lightblue: [173, 216, 230],
    lightcoral: [240, 128, 128],
    lightcyan: [224, 255, 255],
    lightgoldenrodyellow: [250, 250, 210],
    lightgray: [211, 211, 211],
    lightgrey: [211, 211, 211],
    lightgreen: [144, 238, 144],
    lightpink: [255, 182, 193],
    lightsalmon: [255, 160, 122],
    lightseagreen: [32, 178, 170],
    lightskyblue: [135, 206, 250],
    lightslategray: [119, 136, 153],
    lightslategrey: [119, 136, 153],
    lightsteelblue: [176, 196, 222],
    lightyellow: [255, 255, 224],
    lime: [0, 255, 0],
    limegreen: [50, 205, 50],
    linen: [250, 240, 230],
    magenta: [255, 0, 255],
    maroon: [128, 0, 0],
    mediumaquamarine: [102, 205, 170],
    mediumblue: [0, 0, 205],
    mediumorchid: [186, 85, 211],
    mediumpurple: [147, 112, 219],
    mediumseagreen: [60, 179, 113],
    mediumslateblue: [123, 104, 238],
    mediumspringgreen: [0, 250, 154],
    mediumturquoise: [72, 209, 204],
    mediumvioletred: [199, 21, 133],
    midnightblue: [25, 25, 112],
    mintcream: [245, 255, 250],
    mistyrose: [255, 228, 225],
    moccasin: [255, 228, 181],
    navajowhite: [255, 222, 173],
    navy: [0, 0, 128],
    oldlace: [253, 245, 230],
    olive: [128, 128, 0],
    olivedrab: [107, 142, 35],
    orange: [255, 165, 0],
    orangered: [255, 69, 0],
    orchid: [218, 112, 214],
    palegoldenrod: [238, 232, 170],
    palegreen: [152, 251, 152],
    paleturquoise: [175, 238, 238],
    palevioletred: [219, 112, 147],
    papayawhip: [255, 239, 213],
    peachpuff: [255, 218, 185],
    peru: [205, 133, 63],
    pink: [255, 192, 203],
    plum: [221, 160, 221],
    powderblue: [176, 224, 230],
    purple: [128, 0, 128],
    rebeccapurple: [102, 51, 153],
    red: [255, 0, 0],
    rosybrown: [188, 143, 143],
    royalblue: [65, 105, 225],
    saddlebrown: [139, 69, 19],
    salmon: [250, 128, 114],
    sandybrown: [244, 164, 96],
    seagreen: [46, 139, 87],
    seashell: [255, 245, 238],
    sienna: [160, 82, 45],
    silver: [192, 192, 192],
    skyblue: [135, 206, 235],
    slateblue: [106, 90, 205],
    slategray: [112, 128, 144],
    slategrey: [112, 128, 144],
    snow: [255, 250, 250],
    springgreen: [0, 255, 127],
    steelblue: [70, 130, 180],
    tan: [210, 180, 140],
    teal: [0, 128, 128],
    thistle: [216, 191, 216],
    tomato: [255, 99, 71],
    turquoise: [64, 224, 208],
    violet: [238, 130, 238],
    wheat: [245, 222, 179],
    white: [255, 255, 255],
    whitesmoke: [245, 245, 245],
    yellow: [255, 255, 0]
  };
  var DefaultColors = {
    black: [NamedColors.black, 1],
    white: [NamedColors.white, 1],
    transparent: [NamedColors.black, 0]
  };
  var Entities = {
    quot: 34,
    amp: 38,
    lt: 60,
    gt: 62,
    apos: 39,
    OElig: 338,
    oelig: 339,
    Scaron: 352,
    scaron: 353,
    Yuml: 376,
    circ: 710,
    tilde: 732,
    ensp: 8194,
    emsp: 8195,
    thinsp: 8201,
    zwnj: 8204,
    zwj: 8205,
    lrm: 8206,
    rlm: 8207,
    ndash: 8211,
    mdash: 8212,
    lsquo: 8216,
    rsquo: 8217,
    sbquo: 8218,
    ldquo: 8220,
    rdquo: 8221,
    bdquo: 8222,
    dagger: 8224,
    Dagger: 8225,
    permil: 8240,
    lsaquo: 8249,
    rsaquo: 8250,
    euro: 8364,
    nbsp: 160,
    iexcl: 161,
    cent: 162,
    pound: 163,
    curren: 164,
    yen: 165,
    brvbar: 166,
    sect: 167,
    uml: 168,
    copy: 169,
    ordf: 170,
    laquo: 171,
    not: 172,
    shy: 173,
    reg: 174,
    macr: 175,
    deg: 176,
    plusmn: 177,
    sup2: 178,
    sup3: 179,
    acute: 180,
    micro: 181,
    para: 182,
    middot: 183,
    cedil: 184,
    sup1: 185,
    ordm: 186,
    raquo: 187,
    frac14: 188,
    frac12: 189,
    frac34: 190,
    iquest: 191,
    Agrave: 192,
    Aacute: 193,
    Acirc: 194,
    Atilde: 195,
    Auml: 196,
    Aring: 197,
    AElig: 198,
    Ccedil: 199,
    Egrave: 200,
    Eacute: 201,
    Ecirc: 202,
    Euml: 203,
    Igrave: 204,
    Iacute: 205,
    Icirc: 206,
    Iuml: 207,
    ETH: 208,
    Ntilde: 209,
    Ograve: 210,
    Oacute: 211,
    Ocirc: 212,
    Otilde: 213,
    Ouml: 214,
    times: 215,
    Oslash: 216,
    Ugrave: 217,
    Uacute: 218,
    Ucirc: 219,
    Uuml: 220,
    Yacute: 221,
    THORN: 222,
    szlig: 223,
    agrave: 224,
    aacute: 225,
    acirc: 226,
    atilde: 227,
    auml: 228,
    aring: 229,
    aelig: 230,
    ccedil: 231,
    egrave: 232,
    eacute: 233,
    ecirc: 234,
    euml: 235,
    igrave: 236,
    iacute: 237,
    icirc: 238,
    iuml: 239,
    eth: 240,
    ntilde: 241,
    ograve: 242,
    oacute: 243,
    ocirc: 244,
    otilde: 245,
    ouml: 246,
    divide: 247,
    oslash: 248,
    ugrave: 249,
    uacute: 250,
    ucirc: 251,
    uuml: 252,
    yacute: 253,
    thorn: 254,
    yuml: 255,
    fnof: 402,
    Alpha: 913,
    Beta: 914,
    Gamma: 915,
    Delta: 916,
    Epsilon: 917,
    Zeta: 918,
    Eta: 919,
    Theta: 920,
    Iota: 921,
    Kappa: 922,
    Lambda: 923,
    Mu: 924,
    Nu: 925,
    Xi: 926,
    Omicron: 927,
    Pi: 928,
    Rho: 929,
    Sigma: 931,
    Tau: 932,
    Upsilon: 933,
    Phi: 934,
    Chi: 935,
    Psi: 936,
    Omega: 937,
    alpha: 945,
    beta: 946,
    gamma: 947,
    delta: 948,
    epsilon: 949,
    zeta: 950,
    eta: 951,
    theta: 952,
    iota: 953,
    kappa: 954,
    lambda: 955,
    mu: 956,
    nu: 957,
    xi: 958,
    omicron: 959,
    pi: 960,
    rho: 961,
    sigmaf: 962,
    sigma: 963,
    tau: 964,
    upsilon: 965,
    phi: 966,
    chi: 967,
    psi: 968,
    omega: 969,
    thetasym: 977,
    upsih: 978,
    piv: 982,
    bull: 8226,
    hellip: 8230,
    prime: 8242,
    Prime: 8243,
    oline: 8254,
    frasl: 8260,
    weierp: 8472,
    image: 8465,
    real: 8476,
    trade: 8482,
    alefsym: 8501,
    larr: 8592,
    uarr: 8593,
    rarr: 8594,
    darr: 8595,
    harr: 8596,
    crarr: 8629,
    lArr: 8656,
    uArr: 8657,
    rArr: 8658,
    dArr: 8659,
    hArr: 8660,
    forall: 8704,
    part: 8706,
    exist: 8707,
    empty: 8709,
    nabla: 8711,
    isin: 8712,
    notin: 8713,
    ni: 8715,
    prod: 8719,
    sum: 8721,
    minus: 8722,
    lowast: 8727,
    radic: 8730,
    prop: 8733,
    infin: 8734,
    ang: 8736,
    and: 8743,
    or: 8744,
    cap: 8745,
    cup: 8746,
    int: 8747,
    there4: 8756,
    sim: 8764,
    cong: 8773,
    asymp: 8776,
    ne: 8800,
    equiv: 8801,
    le: 8804,
    ge: 8805,
    sub: 8834,
    sup: 8835,
    nsub: 8836,
    sube: 8838,
    supe: 8839,
    oplus: 8853,
    otimes: 8855,
    perp: 8869,
    sdot: 8901,
    lceil: 8968,
    rceil: 8969,
    lfloor: 8970,
    rfloor: 8971,
    lang: 9001,
    rang: 9002,
    loz: 9674,
    spades: 9824,
    clubs: 9827,
    hearts: 9829,
    diams: 9830
  };
  var PathArguments = {
    A: 7,
    a: 7,
    C: 6,
    c: 6,
    H: 1,
    h: 1,
    L: 2,
    l: 2,
    M: 2,
    m: 2,
    Q: 4,
    q: 4,
    S: 4,
    s: 4,
    T: 2,
    t: 2,
    V: 1,
    v: 1,
    Z: 0,
    z: 0
  };
  var PathFlags = {
    A3: true,
    A4: true,
    a3: true,
    a4: true
  };
  var Properties = {
    'color': {
      inherit: true,
      initial: undefined
    },
    'visibility': {
      inherit: true,
      initial: 'visible',
      values: {
        'hidden': 'hidden',
        'collapse': 'hidden',
        'visible': 'visible'
      }
    },
    'fill': {
      inherit: true,
      initial: DefaultColors.black
    },
    'stroke': {
      inherit: true,
      initial: 'none'
    },
    'stop-color': {
      inherit: false,
      initial: DefaultColors.black
    },
    'fill-opacity': {
      inherit: true,
      initial: 1
    },
    'stroke-opacity': {
      inherit: true,
      initial: 1
    },
    'stop-opacity': {
      inherit: false,
      initial: 1
    },
    'fill-rule': {
      inherit: true,
      initial: 'nonzero',
      values: {
        'nonzero': 'nonzero',
        'evenodd': 'evenodd'
      }
    },
    'clip-rule': {
      inherit: true,
      initial: 'nonzero',
      values: {
        'nonzero': 'nonzero',
        'evenodd': 'evenodd'
      }
    },
    'stroke-width': {
      inherit: true,
      initial: 1
    },
    'stroke-dasharray': {
      inherit: true,
      initial: []
    },
    'stroke-dashoffset': {
      inherit: true,
      initial: 0
    },
    'stroke-miterlimit': {
      inherit: true,
      initial: 4
    },
    'stroke-linejoin': {
      inherit: true,
      initial: 'miter',
      values: {
        'miter': 'miter',
        'round': 'round',
        'bevel': 'bevel'
      }
    },
    'stroke-linecap': {
      inherit: true,
      initial: 'butt',
      values: {
        'butt': 'butt',
        'round': 'round',
        'square': 'square'
      }
    },
    'font-size': {
      inherit: true,
      initial: 16,
      values: {
        'xx-small': 9,
        'x-small': 10,
        'small': 13,
        'medium': 16,
        'large': 18,
        'x-large': 24,
        'xx-large': 32
      }
    },
    'font-family': {
      inherit: true,
      initial: 'sans-serif'
    },
    'font-weight': {
      inherit: true,
      initial: 'normal',
      values: {
        '600': 'bold',
        '700': 'bold',
        '800': 'bold',
        '900': 'bold',
        'bold': 'bold',
        'bolder': 'bold',
        '500': 'normal',
        '400': 'normal',
        '300': 'normal',
        '200': 'normal',
        '100': 'normal',
        'normal': 'normal',
        'lighter': 'normal'
      }
    },
    'font-style': {
      inherit: true,
      initial: 'normal',
      values: {
        'italic': 'italic',
        'oblique': 'italic',
        'normal': 'normal'
      }
    },
    'text-anchor': {
      inherit: true,
      initial: 'start',
      values: {
        'start': 'start',
        'middle': 'middle',
        'end': 'end'
      }
    },
    'direction': {
      inherit: true,
      initial: 'ltr',
      values: {
        'ltr': 'ltr',
        'rtl': 'rtl'
      }
    },
    'dominant-baseline': {
      inherit: true,
      initial: 'baseline',
      values: {
        'auto': 'baseline',
        'baseline': 'baseline',
        'before-edge': 'before-edge',
        'text-before-edge': 'before-edge',
        'middle': 'middle',
        'central': 'central',
        'after-edge': 'after-edge',
        'text-after-edge': 'after-edge',
        'ideographic': 'ideographic',
        'alphabetic': 'alphabetic',
        'hanging': 'hanging',
        'mathematical': 'mathematical'
      }
    },
    'alignment-baseline': {
      inherit: false,
      initial: undefined,
      values: {
        'auto': 'baseline',
        'baseline': 'baseline',
        'before-edge': 'before-edge',
        'text-before-edge': 'before-edge',
        'middle': 'middle',
        'central': 'central',
        'after-edge': 'after-edge',
        'text-after-edge': 'after-edge',
        'ideographic': 'ideographic',
        'alphabetic': 'alphabetic',
        'hanging': 'hanging',
        'mathematical': 'mathematical'
      }
    },
    'baseline-shift': {
      inherit: true,
      initial: 'baseline',
      values: {
        'baseline': 'baseline',
        'sub': 'sub',
        'super': 'super'
      }
    },
    'word-spacing': {
      inherit: true,
      initial: 0,
      values: {
        normal: 0
      }
    },
    'letter-spacing': {
      inherit: true,
      initial: 0,
      values: {
        normal: 0
      }
    },
    'text-decoration': {
      inherit: false,
      initial: 'none',
      values: {
        'none': 'none',
        'underline': 'underline',
        'overline': 'overline',
        'line-through': 'line-through'
      }
    },
    'xml:space': {
      inherit: true,
      initial: 'default',
      css: 'white-space',
      values: {
        'preserve': 'preserve',
        'default': 'default',
        'pre': 'preserve',
        'pre-line': 'preserve',
        'pre-wrap': 'preserve',
        'nowrap': 'default'
      }
    },
    'marker-start': {
      inherit: true,
      initial: 'none'
    },
    'marker-mid': {
      inherit: true,
      initial: 'none'
    },
    'marker-end': {
      inherit: true,
      initial: 'none'
    },
    'opacity': {
      inherit: false,
      initial: 1
    },
    'transform': {
      inherit: false,
      initial: [1, 0, 0, 1, 0, 0]
    },
    'display': {
      inherit: false,
      initial: 'inline',
      values: {
        'none': 'none',
        'inline': 'inline',
        'block': 'inline'
      }
    },
    'clip-path': {
      inherit: false,
      initial: 'none'
    },
    'mask': {
      inherit: false,
      initial: 'none'
    },
    'overflow': {
      inherit: false,
      initial: 'hidden',
      values: {
        'hidden': 'hidden',
        'scroll': 'hidden',
        'visible': 'visible'
      }
    }
  };

  function docBeginGroup(bbox) {
    var group = new function PDFGroup() {}();
    group.name = 'G' + (doc._groupCount = (doc._groupCount || 0) + 1);
    group.resources = doc.ref();
    group.xobj = doc.ref({
      Type: 'XObject',
      Subtype: 'Form',
      FormType: 1,
      BBox: bbox,
      Group: {
        S: 'Transparency',
        CS: 'DeviceRGB',
        I: true,
        K: false
      },
      Resources: group.resources
    });
    group.xobj.write('');
    group.savedMatrix = doc._ctm;
    group.savedPage = doc.page;
    groupStack.push(group);
    doc._ctm = [1, 0, 0, 1, 0, 0];
    doc.page = {
      width: doc.page.width,
      height: doc.page.height,
      write: function write(data) {
        group.xobj.write(data);
      },
      fonts: {},
      xobjects: {},
      ext_gstates: {},
      patterns: {}
    };
    return group;
  }

  function docEndGroup(group) {
    if (group !== groupStack.pop()) {
      throw 'Group not matching';
    }

    if (Object.keys(doc.page.fonts).length) {
      group.resources.data.Font = doc.page.fonts;
    }

    if (Object.keys(doc.page.xobjects).length) {
      group.resources.data.XObject = doc.page.xobjects;
    }

    if (Object.keys(doc.page.ext_gstates).length) {
      group.resources.data.ExtGState = doc.page.ext_gstates;
    }

    if (Object.keys(doc.page.patterns).length) {
      group.resources.data.Pattern = doc.page.patterns;
    }

    group.resources.end();
    group.xobj.end();
    doc._ctm = group.savedMatrix;
    doc.page = group.savedPage;
  }

  function docInsertGroup(group) {
    doc.page.xobjects[group.name] = group.xobj;
    doc.addContent('/' + group.name + ' Do');
  }

  function docApplyMask(group, clip) {
    var name = 'M' + (doc._maskCount = (doc._maskCount || 0) + 1);
    var gstate = doc.ref({
      Type: 'ExtGState',
      CA: 1,
      ca: 1,
      BM: 'Normal',
      SMask: {
        S: 'Luminosity',
        G: group.xobj,
        BC: clip ? [0, 0, 0] : [1, 1, 1]
      }
    });
    gstate.end();
    doc.page.ext_gstates[name] = gstate;
    doc.addContent('/' + name + ' gs');
  }

  function docCreatePattern(group, dx, dy, matrix) {
    var pattern = new function PDFPattern() {}();
    pattern.group = group;
    pattern.dx = dx;
    pattern.dy = dy;
    pattern.matrix = matrix || [1, 0, 0, 1, 0, 0];
    return pattern;
  }

  function docUsePattern(pattern, stroke) {
    var name = 'P' + (doc._patternCount = (doc._patternCount || 0) + 1);
    var ref = doc.ref({
      Type: 'Pattern',
      PatternType: 1,
      PaintType: 1,
      TilingType: 2,
      BBox: [0, 0, pattern.dx, pattern.dy],
      XStep: pattern.dx,
      YStep: pattern.dy,
      Matrix: multiplyMatrix(doc._ctm, pattern.matrix),
      Resources: {
        ProcSet: ['PDF', 'Text', 'ImageB', 'ImageC', 'ImageI'],
        XObject: function () {
          var temp = {};
          temp[pattern.group.name] = pattern.group.xobj;
          return temp;
        }()
      }
    });
    ref.write('/' + pattern.group.name + ' Do');
    ref.end();
    doc.page.patterns[name] = ref;

    if (stroke) {
      doc.addContent('/Pattern CS');
      doc.addContent('/' + name + ' SCN');
    } else {
      doc.addContent('/Pattern cs');
      doc.addContent('/' + name + ' scn');
    }
  }

  function docBeginText(font, size) {
    if (!doc.page.fonts[font.id]) {
      doc.page.fonts[font.id] = font.ref();
    }

    doc.addContent('BT').addContent('/' + font.id + ' ' + size + ' Tf');
  }

  function docSetTextMatrix(a, b, c, d, e, f) {
    doc.addContent(validateNumber(a) + ' ' + validateNumber(b) + ' ' + validateNumber(-c) + ' ' + validateNumber(-d) + ' ' + validateNumber(e) + ' ' + validateNumber(f) + ' Tm');
  }

  function docSetTextMode(fill, stroke) {
    var mode = fill && stroke ? 2 : stroke ? 1 : fill ? 0 : 3;
    doc.addContent(mode + ' Tr');
  }

  function docWriteGlyph(glyph) {
    doc.addContent('<' + glyph + '> Tj');
  }

  function docEndText() {
    doc.addContent('ET');
  }

  function docFillColor(color) {
    if (color[0].constructor.name === 'PDFPattern') {
      doc.fillOpacity(color[1]);
      docUsePattern(color[0], false);
    } else {
      doc.fillColor(color[0], color[1]);
    }
  }

  function docStrokeColor(color) {
    if (color[0].constructor.name === 'PDFPattern') {
      doc.strokeOpacity(color[1]);
      docUsePattern(color[0], true);
    } else {
      doc.strokeColor(color[0], color[1]);
    }
  }

  function docInsertLink(x, y, w, h, url) {
    var ref = doc.ref({
      Type: 'Annot',
      Subtype: 'Link',
      Rect: [x, y, w, h],
      Border: [0, 0, 0],
      A: {
        S: 'URI',
        URI: new String(url)
      }
    });
    ref.end();
    links.push(ref);
  }

  function parseXml(xml) {
    var SvgNode = function SvgNode(tag, type, value, error) {
      this.error = error;
      this.nodeName = tag;
      this.nodeValue = value;
      this.nodeType = type;
      this.attributes = Object.create(null);
      this.childNodes = [];
      this.parentNode = null;
      this.id = '';
      this.textContent = '';
      this.classList = [];
    };

    SvgNode.prototype.getAttribute = function (attr) {
      return this.attributes[attr] != null ? this.attributes[attr] : null;
    };

    SvgNode.prototype.getElementById = function (id) {
      var result = null;

      (function recursive(node) {
        if (result) {
          return;
        }

        if (node.nodeType === 1) {
          if (node.id === id) {
            result = node;
          }

          for (var i = 0; i < node.childNodes.length; i++) {
            recursive(node.childNodes[i]);
          }
        }
      })(this);

      return result;
    };

    SvgNode.prototype.getElementsByTagName = function (tag) {
      var result = [];

      (function recursive(node) {
        if (node.nodeType === 1) {
          if (node.nodeName === tag) {
            result.push(node);
          }

          for (var i = 0; i < node.childNodes.length; i++) {
            recursive(node.childNodes[i]);
          }
        }
      })(this);

      return result;
    };

    var parser = new StringParser(xml.trim()),
        result,
        child,
        error = false;

    var recursive = function recursive() {
      var temp, child;

      if (temp = parser.match(/^<([\w:.-]+)\s*/, true)) {
        // Opening tag
        var node = new SvgNode(temp[1], 1, null, error);

        while (temp = parser.match(/^([\w:.-]+)(?:\s*=\s*"([^"]*)"|\s*=\s*'([^']*)')?\s*/, true)) {
          // Attribute
          var attr = temp[1],
              value = decodeEntities(temp[2] || temp[3] || '');

          if (!node.attributes[attr]) {
            node.attributes[attr] = value;

            if (attr === 'id') {
              node.id = value;
            }

            if (attr === 'class') {
              node.classList = value.split(' ');
            }
          } else {
            warningCallback('parseXml: duplicate attribute "' + attr + '"');
            error = true;
          }
        }

        if (parser.match(/^>/)) {
          // End of opening tag
          while (child = recursive()) {
            node.childNodes.push(child);
            child.parentNode = node;
            node.textContent += child.nodeType === 3 || child.nodeType === 4 ? child.nodeValue : child.textContent;
          }

          if (temp = parser.match(/^<\/([\w:.-]+)\s*>/, true)) {
            // Closing tag
            if (temp[1] === node.nodeName) {
              return node;
            } else {
              warningCallback('parseXml: tag not matching, opening "' + node.nodeName + '" & closing "' + temp[1] + '"');
              error = true;
              return node;
            }
          } else {
            warningCallback('parseXml: tag not matching, opening "' + node.nodeName + '" & not closing');
            error = true;
            return node;
          }
        } else if (parser.match(/^\/>/)) {
          // Self-closing tag
          return node;
        } else {
          warningCallback('parseXml: tag could not be parsed "' + node.nodeName + '"');
          error = true;
        }
      } else if (temp = parser.match(/^<!--[\s\S]*?-->/)) {
        // Comment
        return new SvgNode(null, 8, temp, error);
      } else if (temp = parser.match(/^<\?[\s\S]*?\?>/)) {
        // Processing instructions
        return new SvgNode(null, 7, temp, error);
      } else if (temp = parser.match(/^<!DOCTYPE\s*([\s\S]*?)>/)) {
        // Doctype
        return new SvgNode(null, 10, temp, error);
      } else if (temp = parser.match(/^<!\[CDATA\[([\s\S]*?)\]\]>/, true)) {
        // Cdata node
        return new SvgNode('#cdata-section', 4, temp[1], error);
      } else if (temp = parser.match(/^([^<]+)/, true)) {
        // Text node
        return new SvgNode('#text', 3, decodeEntities(temp[1]), error);
      }
    };

    while (child = recursive()) {
      if (child.nodeType === 1 && !result) {
        result = child;
      } else if (child.nodeType === 1 || child.nodeType === 3 && child.nodeValue.trim() !== '') {
        warningCallback('parseXml: data after document end has been discarded');
      }
    }

    if (parser.matchAll()) {
      warningCallback('parseXml: parsing error');
    }

    return result;
  }

  ;

  function decodeEntities(str) {
    return str.replace(/&(?:#([0-9]+)|#[xX]([0-9A-Fa-f]+)|([0-9A-Za-z]+));/g, function (mt, m0, m1, m2) {
      if (m0) {
        return String.fromCharCode(parseInt(m0, 10));
      } else if (m1) {
        return String.fromCharCode(parseInt(m1, 16));
      } else if (m2 && Entities[m2]) {
        return String.fromCharCode(Entities[m2]);
      } else {
        return mt;
      }
    });
  }

  function parseColor(raw) {
    var temp, result;
    raw = (raw || '').trim();

    if (temp = NamedColors[raw]) {
      result = [temp.slice(), 1];
    } else if (temp = raw.match(/^rgba\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9.]+)\s*\)$/i)) {
      temp[1] = parseInt(temp[1]);
      temp[2] = parseInt(temp[2]);
      temp[3] = parseInt(temp[3]);
      temp[4] = parseFloat(temp[4]);

      if (temp[1] < 256 && temp[2] < 256 && temp[3] < 256 && temp[4] <= 1) {
        result = [temp.slice(1, 4), temp[4]];
      }
    } else if (temp = raw.match(/^rgb\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)\s*\)$/i)) {
      temp[1] = parseInt(temp[1]);
      temp[2] = parseInt(temp[2]);
      temp[3] = parseInt(temp[3]);

      if (temp[1] < 256 && temp[2] < 256 && temp[3] < 256) {
        result = [temp.slice(1, 4), 1];
      }
    } else if (temp = raw.match(/^rgb\(\s*([0-9.]+)%\s*,\s*([0-9.]+)%\s*,\s*([0-9.]+)%\s*\)$/i)) {
      temp[1] = 2.55 * parseFloat(temp[1]);
      temp[2] = 2.55 * parseFloat(temp[2]);
      temp[3] = 2.55 * parseFloat(temp[3]);

      if (temp[1] < 256 && temp[2] < 256 && temp[3] < 256) {
        result = [temp.slice(1, 4), 1];
      }
    } else if (temp = raw.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i)) {
      result = [[parseInt(temp[1], 16), parseInt(temp[2], 16), parseInt(temp[3], 16)], 1];
    } else if (temp = raw.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i)) {
      result = [[0x11 * parseInt(temp[1], 16), 0x11 * parseInt(temp[2], 16), 0x11 * parseInt(temp[3], 16)], 1];
    }

    return colorCallback ? colorCallback(result, raw) : result;
  }

  function opacityToColor(color, opacity, isMask) {
    var newColor = color[0].slice(),
        newOpacity = color[1] * opacity;

    if (isMask) {
      for (var i = 0; i < color.length; i++) {
        newColor[i] *= newOpacity;
      }

      return [newColor, 1];
    } else {
      return [newColor, newOpacity];
    }
  }

  function multiplyMatrix() {
    function multiply(a, b) {
      return [a[0] * b[0] + a[2] * b[1], a[1] * b[0] + a[3] * b[1], a[0] * b[2] + a[2] * b[3], a[1] * b[2] + a[3] * b[3], a[0] * b[4] + a[2] * b[5] + a[4], a[1] * b[4] + a[3] * b[5] + a[5]];
    }

    var result = arguments[0];

    for (var i = 1; i < arguments.length; i++) {
      result = multiply(result, arguments[i]);
    }

    return result;
  }

  function transformPoint(p, m) {
    return [m[0] * p[0] + m[2] * p[1] + m[4], m[1] * p[0] + m[3] * p[1] + m[5]];
  }

  function getGlobalMatrix() {
    var ctm = doc._ctm;

    for (var i = groupStack.length - 1; i >= 0; i--) {
      ctm = multiplyMatrix(groupStack[i].savedMatrix, ctm);
    }

    return ctm;
  }

  function getPageBBox() {
    return new SvgShape().M(0, 0).L(doc.page.width, 0).L(doc.page.width, doc.page.height).L(0, doc.page.height).transform(inverseMatrix(getGlobalMatrix())).getBoundingBox();
  }

  function inverseMatrix(m) {
    var dt = m[0] * m[3] - m[1] * m[2];
    return [m[3] / dt, -m[1] / dt, -m[2] / dt, m[0] / dt, (m[2] * m[5] - m[3] * m[4]) / dt, (m[1] * m[4] - m[0] * m[5]) / dt];
  }

  function validateMatrix(m) {
    var m0 = validateNumber(m[0]),
        m1 = validateNumber(m[1]),
        m2 = validateNumber(m[2]),
        m3 = validateNumber(m[3]),
        m4 = validateNumber(m[4]),
        m5 = validateNumber(m[5]);

    if (isNotEqual(m0 * m3 - m1 * m2, 0)) {
      return [m0, m1, m2, m3, m4, m5];
    }
  }

  function solveEquation(curve) {
    var a = curve[2] || 0,
        b = curve[1] || 0,
        c = curve[0] || 0;

    if (isEqual(a, 0) && isEqual(b, 0)) {
      return [];
    } else if (isEqual(a, 0)) {
      return [-c / b];
    } else {
      var d = b * b - 4 * a * c;

      if (isNotEqual(d, 0) && d > 0) {
        return [(-b + Math.sqrt(d)) / (2 * a), (-b - Math.sqrt(d)) / (2 * a)];
      } else if (isEqual(d, 0)) {
        return [-b / (2 * a)];
      } else {
        return [];
      }
    }
  }

  function getCurveValue(t, curve) {
    return (curve[0] || 0) + (curve[1] || 0) * t + (curve[2] || 0) * t * t + (curve[3] || 0) * t * t * t;
  }

  function isEqual(number, ref) {
    return Math.abs(number - ref) < 1e-10;
  }

  function isNotEqual(number, ref) {
    return Math.abs(number - ref) >= 1e-10;
  }

  function validateNumber(n) {
    return n > -1e21 && n < 1e21 ? Math.round(n * 1e6) / 1e6 : 0;
  }

  function isArrayLike(v) {
    return typeof v === 'object' && v !== null && typeof v.length === 'number';
  }

  function parseTranform(v) {
    var parser = new StringParser((v || '').trim()),
        result = [1, 0, 0, 1, 0, 0],
        temp;

    while (temp = parser.match(/^([A-Za-z]+)\s*[(]([^(]+)[)]/, true)) {
      var func = temp[1],
          nums = [],
          parser2 = new StringParser(temp[2].trim()),
          temp2 = void 0;

      while (temp2 = parser2.matchNumber()) {
        nums.push(Number(temp2));
        parser2.matchSeparator();
      }

      if (func === 'matrix' && nums.length === 6) {
        result = multiplyMatrix(result, [nums[0], nums[1], nums[2], nums[3], nums[4], nums[5]]);
      } else if (func === 'translate' && nums.length === 2) {
        result = multiplyMatrix(result, [1, 0, 0, 1, nums[0], nums[1]]);
      } else if (func === 'translate' && nums.length === 1) {
        result = multiplyMatrix(result, [1, 0, 0, 1, nums[0], 0]);
      } else if (func === 'scale' && nums.length === 2) {
        result = multiplyMatrix(result, [nums[0], 0, 0, nums[1], 0, 0]);
      } else if (func === 'scale' && nums.length === 1) {
        result = multiplyMatrix(result, [nums[0], 0, 0, nums[0], 0, 0]);
      } else if (func === 'rotate' && nums.length === 3) {
        var a = nums[0] * Math.PI / 180;
        result = multiplyMatrix(result, [1, 0, 0, 1, nums[1], nums[2]], [Math.cos(a), Math.sin(a), -Math.sin(a), Math.cos(a), 0, 0], [1, 0, 0, 1, -nums[1], -nums[2]]);
      } else if (func === 'rotate' && nums.length === 1) {
        var _a = nums[0] * Math.PI / 180;

        result = multiplyMatrix(result, [Math.cos(_a), Math.sin(_a), -Math.sin(_a), Math.cos(_a), 0, 0]);
      } else if (func === 'skewX' && nums.length === 1) {
        var _a2 = nums[0] * Math.PI / 180;

        result = multiplyMatrix(result, [1, 0, Math.tan(_a2), 1, 0, 0]);
      } else if (func === 'skewY' && nums.length === 1) {
        var _a3 = nums[0] * Math.PI / 180;

        result = multiplyMatrix(result, [1, Math.tan(_a3), 0, 1, 0, 0]);
      } else {
        return;
      }

      parser.matchSeparator();
    }

    if (parser.matchAll()) {
      return;
    }

    return result;
  }

  function parseAspectRatio(aspectRatio, availWidth, availHeight, elemWidth, elemHeight, initAlign) {
    var temp = (aspectRatio || '').trim().match(/^(none)$|^x(Min|Mid|Max)Y(Min|Mid|Max)(?:\s+(meet|slice))?$/) || [],
        ratioType = temp[1] || temp[4] || 'meet',
        xAlign = temp[2] || 'Mid',
        yAlign = temp[3] || 'Mid',
        scaleX = availWidth / elemWidth,
        scaleY = availHeight / elemHeight,
        dx = {
      'Min': 0,
      'Mid': 0.5,
      'Max': 1
    }[xAlign] - (initAlign || 0),
        dy = {
      'Min': 0,
      'Mid': 0.5,
      'Max': 1
    }[yAlign] - (initAlign || 0);

    if (ratioType === 'slice') {
      scaleY = scaleX = Math.max(scaleX, scaleY);
    } else if (ratioType === 'meet') {
      scaleY = scaleX = Math.min(scaleX, scaleY);
    }

    return [scaleX, 0, 0, scaleY, dx * (availWidth - elemWidth * scaleX), dy * (availHeight - elemHeight * scaleY)];
  }

  function parseStyleAttr(v) {
    var result = Object.create(null);
    v = (v || '').trim().split(/;/);

    for (var i = 0; i < v.length; i++) {
      var key = (v[i].split(':')[0] || '').trim(),
          value = (v[i].split(':')[1] || '').trim();

      if (key) {
        result[key] = value;
      }
    }

    if (result['marker']) {
      if (!result['marker-start']) {
        result['marker-start'] = result['marker'];
      }

      if (!result['marker-mid']) {
        result['marker-mid'] = result['marker'];
      }

      if (!result['marker-end']) {
        result['marker-end'] = result['marker'];
      }
    }

    if (result['font']) {
      var fontFamily = null,
          fontSize = null,
          fontStyle = "normal",
          fontWeight = "normal",
          fontVariant = "normal";
      var parts = result['font'].split(/\s+/);

      for (var _i = 0; _i < parts.length; _i++) {
        switch (parts[_i]) {
          case "normal":
            break;

          case "italic":
          case "oblique":
            fontStyle = parts[_i];
            break;

          case "small-caps":
            fontVariant = parts[_i];
            break;

          case "bold":
          case "bolder":
          case "lighter":
          case "100":
          case "200":
          case "300":
          case "400":
          case "500":
          case "600":
          case "700":
          case "800":
          case "900":
            fontWeight = parts[_i];
            break;

          default:
            if (!fontSize) {
              fontSize = parts[_i].split('/')[0];
            } else {
              if (!fontFamily) {
                fontFamily = parts[_i];
              } else {
                fontFamily += ' ' + parts[_i];
              }
            }

            break;
        }
      }

      if (!result['font-style']) {
        result['font-style'] = fontStyle;
      }

      if (!result['font-variant']) {
        result['font-variant'] = fontVariant;
      }

      if (!result['font-weight']) {
        result['font-weight'] = fontWeight;
      }

      if (!result['font-size']) {
        result['font-size'] = fontSize;
      }

      if (!result['font-family']) {
        result['font-family'] = fontFamily;
      }
    }

    return result;
  }

  function parseSelector(v) {
    var parts = v.split(/(?=[.#])/g),
        ids = [],
        classes = [],
        tags = [],
        temp;

    for (var i = 0; i < parts.length; i++) {
      if (temp = parts[i].match(/^[#]([_A-Za-z0-9-]+)$/)) {
        ids.push(temp[1]);
      } else if (temp = parts[i].match(/^[.]([_A-Za-z0-9-]+)$/)) {
        classes.push(temp[1]);
      } else if (temp = parts[i].match(/^([_A-Za-z0-9-]+)$/)) {
        tags.push(temp[1]);
      } else if (parts[i] !== '*') {
        return;
      }
    }

    return {
      tags: tags,
      ids: ids,
      classes: classes,
      specificity: ids.length * 10000 + classes.length * 100 + tags.length
    };
  }

  function parseStyleSheet(v) {
    var parser = new StringParser(v.trim()),
        rules = [],
        rule;

    while (rule = parser.match(/^\s*([^\{\}]*?)\s*\{([^\{\}]*?)\}/, true)) {
      var selectors = rule[1].split(/\s*,\s*/g),
          css = parseStyleAttr(rule[2]);

      for (var i = 0; i < selectors.length; i++) {
        var selector = parseSelector(selectors[i]);

        if (selector) {
          rules.push({
            selector: selector,
            css: css
          });
        }
      }
    }

    return rules;
  }

  function matchesSelector(elem, selector) {
    if (elem.nodeType !== 1) {
      return false;
    }

    for (var i = 0; i < selector.tags.length; i++) {
      if (selector.tags[i] !== elem.nodeName) {
        return false;
      }
    }

    for (var _i2 = 0; _i2 < selector.ids.length; _i2++) {
      if (selector.ids[_i2] !== elem.id) {
        return false;
      }
    }

    for (var _i3 = 0; _i3 < selector.classes.length; _i3++) {
      if (elem.classList.indexOf(selector.classes[_i3]) === -1) {
        return false;
      }
    }

    return true;
  }

  function getStyle(elem) {
    var result = Object.create(null);
    var specificities = Object.create(null);

    for (var i = 0; i < styleRules.length; i++) {
      var rule = styleRules[i];

      if (matchesSelector(elem, rule.selector)) {
        for (var key in rule.css) {
          if (!(specificities[key] > rule.selector.specificity)) {
            result[key] = rule.css[key];
            specificities[key] = rule.selector.specificity;
          }
        }
      }
    }

    return result;
  }

  function combineArrays(array1, array2) {
    return array1.concat(array2.slice(array1.length));
  }

  function getAscent(font, size) {
    return Math.max(font.ascender, (font.bbox[3] || font.bbox.maxY) * (font.scale || 1)) * size / 1000;
  }

  function getDescent(font, size) {
    return Math.min(font.descender, (font.bbox[1] || font.bbox.minY) * (font.scale || 1)) * size / 1000;
  }

  function getXHeight(font, size) {
    return (font.xHeight || 0.5 * (font.ascender - font.descender)) * size / 1000;
  }

  function getBaseline(font, size, baseline, shift) {
    var dy1, dy2;

    switch (baseline) {
      case 'middle':
        dy1 = 0.5 * getXHeight(font, size);
        break;

      case 'central':
        dy1 = 0.5 * (getDescent(font, size) + getAscent(font, size));
        break;

      case 'after-edge':
      case 'text-after-edge':
        dy1 = getDescent(font, size);
        break;

      case 'alphabetic':
      case 'auto':
      case 'baseline':
        dy1 = 0;
        break;

      case 'mathematical':
        dy1 = 0.5 * getAscent(font, size);
        break;

      case 'hanging':
        dy1 = 0.8 * getAscent(font, size);
        break;

      case 'before-edge':
      case 'text-before-edge':
        dy1 = getAscent(font, size);
        break;

      default:
        dy1 = 0;
        break;
    }

    switch (shift) {
      case 'baseline':
        dy2 = 0;
        break;

      case 'super':
        dy2 = 0.6 * size;
        break;

      case 'sub':
        dy2 = -0.6 * size;
        break;

      default:
        dy2 = shift;
        break;
    }

    return dy1 - dy2;
  }

  function getTextPos(font, size, text) {
    var encoded = font.encode('' + text),
        hex = encoded[0],
        pos = encoded[1],
        data = [];

    for (var i = 0; i < hex.length; i++) {
      var unicode = font.unicode ? font.unicode[parseInt(hex[i], 16)] : [text.charCodeAt(i)];
      data.push({
        glyph: hex[i],
        unicode: unicode,
        width: pos[i].advanceWidth * size / 1000,
        xOffset: pos[i].xOffset * size / 1000,
        yOffset: pos[i].yOffset * size / 1000,
        xAdvance: pos[i].xAdvance * size / 1000,
        yAdvance: pos[i].yAdvance * size / 1000
      });
    }

    return data;
  }

  function createSVGElement(obj, inherits) {
    switch (obj.nodeName) {
      case 'use':
        return new SvgElemUse(obj, inherits);

      case 'symbol':
        return new SvgElemSymbol(obj, inherits);

      case 'g':
        return new SvgElemGroup(obj, inherits);

      case 'a':
        return new SvgElemLink(obj, inherits);

      case 'svg':
        return new SvgElemSvg(obj, inherits);

      case 'image':
        return new SVGElemImage(obj, inherits);

      case 'rect':
        return new SvgElemRect(obj, inherits);

      case 'circle':
        return new SvgElemCircle(obj, inherits);

      case 'ellipse':
        return new SvgElemEllipse(obj, inherits);

      case 'line':
        return new SvgElemLine(obj, inherits);

      case 'polyline':
        return new SvgElemPolyline(obj, inherits);

      case 'polygon':
        return new SvgElemPolygon(obj, inherits);

      case 'path':
        return new SvgElemPath(obj, inherits);

      case 'text':
        return new SvgElemText(obj, inherits);

      case 'tspan':
        return new SvgElemTspan(obj, inherits);

      case 'textPath':
        return new SvgElemTextPath(obj, inherits);

      case '#text':
      case '#cdata-section':
        return new SvgElemTextNode(obj, inherits);

      default:
        return new SvgElem(obj, inherits);
    }
  }

  var StringParser = function StringParser(str) {
    this.match = function (exp, all) {
      var temp = str.match(exp);

      if (!temp || temp.index !== 0) {
        return;
      }

      str = str.substring(temp[0].length);
      return all ? temp : temp[0];
    };

    this.matchSeparator = function () {
      return this.match(/^(?:\s*,\s*|\s*|)/);
    };

    this.matchSpace = function () {
      return this.match(/^(?:\s*)/);
    };

    this.matchLengthUnit = function () {
      return this.match(/^(?:px|pt|cm|mm|in|pc|em|ex|%|)/);
    };

    this.matchNumber = function () {
      return this.match(/^(?:[-+]?(?:[0-9]+[.][0-9]+|[0-9]+[.]|[.][0-9]+|[0-9]+)(?:[eE][-+]?[0-9]+)?)/);
    };

    this.matchAll = function () {
      return this.match(/^[\s\S]+/);
    };
  };

  var BezierSegment = function BezierSegment(p1x, p1y, c1x, c1y, c2x, c2y, p2x, p2y) {
    var divisions = 6 * precision;
    var equationX = [p1x, -3 * p1x + 3 * c1x, 3 * p1x - 6 * c1x + 3 * c2x, -p1x + 3 * c1x - 3 * c2x + p2x];
    var equationY = [p1y, -3 * p1y + 3 * c1y, 3 * p1y - 6 * c1y + 3 * c2y, -p1y + 3 * c1y - 3 * c2y + p2y];
    var derivativeX = [-3 * p1x + 3 * c1x, 6 * p1x - 12 * c1x + 6 * c2x, -3 * p1x + 9 * c1x - 9 * c2x + 3 * p2x];
    var derivativeY = [-3 * p1y + 3 * c1y, 6 * p1y - 12 * c1y + 6 * c2y, -3 * p1y + 9 * c1y - 9 * c2y + 3 * p2y];
    var lengthMap = [0];

    for (var i = 1; i <= divisions; i++) {
      var t = (i - 0.5) / divisions;
      var dx = getCurveValue(t, derivativeX) / divisions,
          dy = getCurveValue(t, derivativeY) / divisions,
          l = Math.sqrt(dx * dx + dy * dy);
      lengthMap[i] = lengthMap[i - 1] + l;
    }

    this.totalLength = lengthMap[divisions];
    this.startPoint = [p1x, p1y, isEqual(p1x, c1x) && isEqual(p1y, c1y) ? Math.atan2(c2y - c1y, c2x - c1x) : Math.atan2(c1y - p1y, c1x - p1x)];
    this.endPoint = [p2x, p2y, isEqual(c2x, p2x) && isEqual(c2y, p2y) ? Math.atan2(c2y - c1y, c2x - c1x) : Math.atan2(p2y - c2y, p2x - c2x)];

    this.getBoundingBox = function () {
      var temp;
      var minX = getCurveValue(0, equationX),
          minY = getCurveValue(0, equationY),
          maxX = getCurveValue(1, equationX),
          maxY = getCurveValue(1, equationY);

      if (minX > maxX) {
        temp = maxX;
        maxX = minX;
        minX = temp;
      }

      if (minY > maxY) {
        temp = maxY;
        maxY = minY;
        minY = temp;
      }

      var rootsX = solveEquation(derivativeX);

      for (var _i4 = 0; _i4 < rootsX.length; _i4++) {
        if (rootsX[_i4] >= 0 && rootsX[_i4] <= 1) {
          var _x = getCurveValue(rootsX[_i4], equationX);

          if (_x < minX) {
            minX = _x;
          }

          if (_x > maxX) {
            maxX = _x;
          }
        }
      }

      var rootsY = solveEquation(derivativeY);

      for (var _i5 = 0; _i5 < rootsY.length; _i5++) {
        if (rootsY[_i5] >= 0 && rootsY[_i5] <= 1) {
          var _y = getCurveValue(rootsY[_i5], equationY);

          if (_y < minY) {
            minY = _y;
          }

          if (_y > maxY) {
            maxY = _y;
          }
        }
      }

      return [minX, minY, maxX, maxY];
    };

    this.getPointAtLength = function (l) {
      if (isEqual(l, 0)) {
        return this.startPoint;
      }

      if (isEqual(l, this.totalLength)) {
        return this.endPoint;
      }

      if (l < 0 || l > this.totalLength) {
        return;
      }

      for (var _i6 = 1; _i6 <= divisions; _i6++) {
        var l1 = lengthMap[_i6 - 1],
            l2 = lengthMap[_i6];

        if (l1 <= l && l <= l2) {
          var _t = (_i6 - (l2 - l) / (l2 - l1)) / divisions,
              _x2 = getCurveValue(_t, equationX),
              _y2 = getCurveValue(_t, equationY),
              _dx = getCurveValue(_t, derivativeX),
              _dy = getCurveValue(_t, derivativeY);

          return [_x2, _y2, Math.atan2(_dy, _dx)];
        }
      }
    };
  };

  var LineSegment = function LineSegment(p1x, p1y, p2x, p2y) {
    this.totalLength = Math.sqrt((p2x - p1x) * (p2x - p1x) + (p2y - p1y) * (p2y - p1y));
    this.startPoint = [p1x, p1y, Math.atan2(p2y - p1y, p2x - p1x)];
    this.endPoint = [p2x, p2y, Math.atan2(p2y - p1y, p2x - p1x)];

    this.getBoundingBox = function () {
      return [Math.min(this.startPoint[0], this.endPoint[0]), Math.min(this.startPoint[1], this.endPoint[1]), Math.max(this.startPoint[0], this.endPoint[0]), Math.max(this.startPoint[1], this.endPoint[1])];
    };

    this.getPointAtLength = function (l) {
      if (l >= 0 && l <= this.totalLength) {
        var r = l / this.totalLength || 0,
            _x3 = this.startPoint[0] + r * (this.endPoint[0] - this.startPoint[0]),
            _y3 = this.startPoint[1] + r * (this.endPoint[1] - this.startPoint[1]);

        return [_x3, _y3, this.startPoint[2]];
      }
    };
  };

  var SvgShape = function SvgShape() {
    this.pathCommands = [];
    this.pathSegments = [];
    this.startPoint = null;
    this.endPoint = null;
    this.totalLength = 0;
    var startX = 0,
        startY = 0,
        currX = 0,
        currY = 0,
        lastCom,
        lastCtrlX,
        lastCtrlY;

    this.move = function (x, y) {
      startX = currX = x;
      startY = currY = y;
      return null;
    };

    this.line = function (x, y) {
      var segment = new LineSegment(currX, currY, x, y);
      currX = x;
      currY = y;
      return segment;
    };

    this.curve = function (c1x, c1y, c2x, c2y, x, y) {
      var segment = new BezierSegment(currX, currY, c1x, c1y, c2x, c2y, x, y);
      currX = x;
      currY = y;
      return segment;
    };

    this.close = function () {
      var segment = new LineSegment(currX, currY, startX, startY);
      currX = startX;
      currY = startY;
      return segment;
    };

    this.addCommand = function (data) {
      this.pathCommands.push(data);
      var segment = this[data[0]].apply(this, data.slice(3));

      if (segment) {
        segment.hasStart = data[1];
        segment.hasEnd = data[2];
        this.startPoint = this.startPoint || segment.startPoint;
        this.endPoint = segment.endPoint;
        this.pathSegments.push(segment);
        this.totalLength += segment.totalLength;
      }
    };

    this.M = function (x, y) {
      this.addCommand(['move', true, true, x, y]);
      lastCom = 'M';
      return this;
    };

    this.m = function (x, y) {
      return this.M(currX + x, currY + y);
    };

    this.Z = this.z = function () {
      this.addCommand(['close', true, true]);
      lastCom = 'Z';
      return this;
    };

    this.L = function (x, y) {
      this.addCommand(['line', true, true, x, y]);
      lastCom = 'L';
      return this;
    };

    this.l = function (x, y) {
      return this.L(currX + x, currY + y);
    };

    this.H = function (x) {
      return this.L(x, currY);
    };

    this.h = function (x) {
      return this.L(currX + x, currY);
    };

    this.V = function (y) {
      return this.L(currX, y);
    };

    this.v = function (y) {
      return this.L(currX, currY + y);
    };

    this.C = function (c1x, c1y, c2x, c2y, x, y) {
      this.addCommand(['curve', true, true, c1x, c1y, c2x, c2y, x, y]);
      lastCom = 'C';
      lastCtrlX = c2x;
      lastCtrlY = c2y;
      return this;
    };

    this.c = function (c1x, c1y, c2x, c2y, x, y) {
      return this.C(currX + c1x, currY + c1y, currX + c2x, currY + c2y, currX + x, currY + y);
    };

    this.S = function (c1x, c1y, x, y) {
      return this.C(currX + (lastCom === 'C' ? currX - lastCtrlX : 0), currY + (lastCom === 'C' ? currY - lastCtrlY : 0), c1x, c1y, x, y);
    };

    this.s = function (c1x, c1y, x, y) {
      return this.C(currX + (lastCom === 'C' ? currX - lastCtrlX : 0), currY + (lastCom === 'C' ? currY - lastCtrlY : 0), currX + c1x, currY + c1y, currX + x, currY + y);
    };

    this.Q = function (cx, cy, x, y) {
      var c1x = currX + 2 / 3 * (cx - currX),
          c1y = currY + 2 / 3 * (cy - currY),
          c2x = x + 2 / 3 * (cx - x),
          c2y = y + 2 / 3 * (cy - y);
      this.addCommand(['curve', true, true, c1x, c1y, c2x, c2y, x, y]);
      lastCom = 'Q';
      lastCtrlX = cx;
      lastCtrlY = cy;
      return this;
    };

    this.q = function (c1x, c1y, x, y) {
      return this.Q(currX + c1x, currY + c1y, currX + x, currY + y);
    };

    this.T = function (x, y) {
      return this.Q(currX + (lastCom === 'Q' ? currX - lastCtrlX : 0), currY + (lastCom === 'Q' ? currY - lastCtrlY : 0), x, y);
    };

    this.t = function (x, y) {
      return this.Q(currX + (lastCom === 'Q' ? currX - lastCtrlX : 0), currY + (lastCom === 'Q' ? currY - lastCtrlY : 0), currX + x, currY + y);
    };

    this.A = function (rx, ry, fi, fa, fs, x, y) {
      if (isEqual(rx, 0) || isEqual(ry, 0)) {
        this.addCommand(['line', true, true, x, y]);
      } else {
        fi = fi * (Math.PI / 180);
        rx = Math.abs(rx);
        ry = Math.abs(ry);
        fa = 1 * !!fa;
        fs = 1 * !!fs;
        var x1 = Math.cos(fi) * (currX - x) / 2 + Math.sin(fi) * (currY - y) / 2,
            y1 = Math.cos(fi) * (currY - y) / 2 - Math.sin(fi) * (currX - x) / 2,
            lambda = x1 * x1 / (rx * rx) + y1 * y1 / (ry * ry);

        if (lambda > 1) {
          rx *= Math.sqrt(lambda);
          ry *= Math.sqrt(lambda);
        }

        var r = Math.sqrt(Math.max(0, rx * rx * ry * ry - rx * rx * y1 * y1 - ry * ry * x1 * x1) / (rx * rx * y1 * y1 + ry * ry * x1 * x1)),
            x2 = (fa === fs ? -1 : 1) * r * rx * y1 / ry,
            y2 = (fa === fs ? 1 : -1) * r * ry * x1 / rx;
        var cx = Math.cos(fi) * x2 - Math.sin(fi) * y2 + (currX + x) / 2,
            cy = Math.sin(fi) * x2 + Math.cos(fi) * y2 + (currY + y) / 2,
            th1 = Math.atan2((y1 - y2) / ry, (x1 - x2) / rx),
            th2 = Math.atan2((-y1 - y2) / ry, (-x1 - x2) / rx);

        if (fs === 0 && th2 - th1 > 0) {
          th2 -= 2 * Math.PI;
        } else if (fs === 1 && th2 - th1 < 0) {
          th2 += 2 * Math.PI;
        }

        var segms = Math.ceil(Math.abs(th2 - th1) / (Math.PI / precision));

        for (var i = 0; i < segms; i++) {
          var th3 = th1 + i * (th2 - th1) / segms,
              th4 = th1 + (i + 1) * (th2 - th1) / segms,
              t = 4 / 3 * Math.tan((th4 - th3) / 4);
          var c1x = cx + Math.cos(fi) * rx * (Math.cos(th3) - t * Math.sin(th3)) - Math.sin(fi) * ry * (Math.sin(th3) + t * Math.cos(th3)),
              c1y = cy + Math.sin(fi) * rx * (Math.cos(th3) - t * Math.sin(th3)) + Math.cos(fi) * ry * (Math.sin(th3) + t * Math.cos(th3)),
              c2x = cx + Math.cos(fi) * rx * (Math.cos(th4) + t * Math.sin(th4)) - Math.sin(fi) * ry * (Math.sin(th4) - t * Math.cos(th4)),
              c2y = cy + Math.sin(fi) * rx * (Math.cos(th4) + t * Math.sin(th4)) + Math.cos(fi) * ry * (Math.sin(th4) - t * Math.cos(th4)),
              endX = cx + Math.cos(fi) * rx * Math.cos(th4) - Math.sin(fi) * ry * Math.sin(th4),
              endY = cy + Math.sin(fi) * rx * Math.cos(th4) + Math.cos(fi) * ry * Math.sin(th4);
          this.addCommand(['curve', i === 0, i === segms - 1, c1x, c1y, c2x, c2y, endX, endY]);
        }
      }

      lastCom = 'A';
      return this;
    };

    this.a = function (rx, ry, fi, fa, fs, x, y) {
      return this.A(rx, ry, fi, fa, fs, currX + x, currY + y);
    };

    this.path = function (d) {
      var command,
          value,
          temp,
          parser = new StringParser((d || '').trim());

      while (command = parser.match(/^[astvzqmhlcASTVZQMHLC]/)) {
        parser.matchSeparator();
        var values = [];

        while (value = PathFlags[command + values.length] ? parser.match(/^[01]/) : parser.matchNumber()) {
          parser.matchSeparator();

          if (values.length === PathArguments[command]) {
            this[command].apply(this, values);
            values = [];

            if (command === 'M') {
              command = 'L';
            } else if (command === 'm') {
              command = 'l';
            }
          }

          values.push(Number(value));
        }

        if (values.length === PathArguments[command]) {
          this[command].apply(this, values);
        } else {
          warningCallback('SvgPath: command ' + command + ' with ' + values.length + ' numbers');
          return;
        }
      }

      if (temp = parser.matchAll()) {
        warningCallback('SvgPath: unexpected string ' + temp);
      }

      return this;
    };

    this.getBoundingBox = function () {
      var bbox = [Infinity, Infinity, -Infinity, -Infinity];

      function addBounds(bbox1) {
        if (bbox1[0] < bbox[0]) {
          bbox[0] = bbox1[0];
        }

        if (bbox1[2] > bbox[2]) {
          bbox[2] = bbox1[2];
        }

        if (bbox1[1] < bbox[1]) {
          bbox[1] = bbox1[1];
        }

        if (bbox1[3] > bbox[3]) {
          bbox[3] = bbox1[3];
        }
      }

      for (var i = 0; i < this.pathSegments.length; i++) {
        addBounds(this.pathSegments[i].getBoundingBox());
      }

      if (bbox[0] === Infinity) {
        bbox[0] = 0;
      }

      if (bbox[1] === Infinity) {
        bbox[1] = 0;
      }

      if (bbox[2] === -Infinity) {
        bbox[2] = 0;
      }

      if (bbox[3] === -Infinity) {
        bbox[3] = 0;
      }

      return bbox;
    };

    this.getPointAtLength = function (l) {
      if (l >= 0 && l <= this.totalLength) {
        var temp;

        for (var i = 0; i < this.pathSegments.length; i++) {
          if (temp = this.pathSegments[i].getPointAtLength(l)) {
            return temp;
          }

          l -= this.pathSegments[i].totalLength;
        }

        return this.endPoint;
      }
    };

    this.transform = function (m) {
      this.pathSegments = [];
      this.startPoint = null;
      this.endPoint = null;
      this.totalLength = 0;

      for (var i = 0; i < this.pathCommands.length; i++) {
        var data = this.pathCommands.shift();

        for (var j = 3; j < data.length; j += 2) {
          var p = transformPoint([data[j], data[j + 1]], m);
          data[j] = p[0];
          data[j + 1] = p[1];
        }

        this.addCommand(data);
      }

      return this;
    };

    this.mergeShape = function (shape) {
      for (var i = 0; i < shape.pathCommands.length; i++) {
        this.addCommand(shape.pathCommands[i].slice());
      }

      return this;
    };

    this.clone = function () {
      return new SvgShape().mergeShape(this);
    };

    this.insertInDocument = function () {
      for (var i = 0; i < this.pathCommands.length; i++) {
        var command = this.pathCommands[i][0],
            values = this.pathCommands[i].slice(3);

        switch (command) {
          case 'move':
            doc.moveTo(values[0], values[1]);
            break;

          case 'line':
            doc.lineTo(values[0], values[1]);
            break;

          case 'curve':
            doc.bezierCurveTo(values[0], values[1], values[2], values[3], values[4], values[5]);
            break;

          case 'close':
            doc.closePath();
            break;
        }
      }
    };

    this.getSubPaths = function () {
      var subPaths = [],
          shape = new SvgShape();

      for (var i = 0; i < this.pathCommands.length; i++) {
        var data = this.pathCommands[i],
            command = this.pathCommands[i][0];

        if (command === 'move' && i !== 0) {
          subPaths.push(shape);
          shape = new SvgShape();
        }

        shape.addCommand(data);
      }

      subPaths.push(shape);
      return subPaths;
    };

    this.getMarkers = function () {
      var markers = [],
          subPaths = this.getSubPaths();

      for (var i = 0; i < subPaths.length; i++) {
        var subPath = subPaths[i],
            subPathMarkers = [];

        for (var j = 0; j < subPath.pathSegments.length; j++) {
          var segment = subPath.pathSegments[j];

          if (isNotEqual(segment.totalLength, 0) || j === 0 || j === subPath.pathSegments.length - 1) {
            if (segment.hasStart) {
              var startMarker = segment.getPointAtLength(0),
                  prevEndMarker = subPathMarkers.pop();

              if (prevEndMarker) {
                startMarker[2] = 0.5 * (prevEndMarker[2] + startMarker[2]);
              }

              subPathMarkers.push(startMarker);
            }

            if (segment.hasEnd) {
              var endMarker = segment.getPointAtLength(segment.totalLength);
              subPathMarkers.push(endMarker);
            }
          }
        }

        markers = markers.concat(subPathMarkers);
      }

      return markers;
    };
  };

  var SvgElem = function SvgElem(obj, inherits) {
    var styleCache = Object.create(null);
    var childrenCache = null;
    this.name = obj.nodeName;
    this.isOuterElement = obj === svg || !obj.parentNode;
    this.inherits = inherits || (!this.isOuterElement ? createSVGElement(obj.parentNode, null) : null);
    this.stack = this.inherits ? this.inherits.stack.concat(obj) : [obj];
    this.style = parseStyleAttr(typeof obj.getAttribute === 'function' && obj.getAttribute('style'));
    this.css = useCSS ? getComputedStyle(obj) : getStyle(obj);
    this.allowedChildren = [];

    this.attr = function (key) {
      if (typeof obj.getAttribute === 'function') {
        return obj.getAttribute(key);
      }
    };

    this.resolveUrl = function (value) {
      var temp = (value || '').match(/^\s*(?:url\("(.*)#(.*)"\)|url\('(.*)#(.*)'\)|url\((.*)#(.*)\)|(.*)#(.*))\s*$/) || [];
      var file = temp[1] || temp[3] || temp[5] || temp[7],
          id = temp[2] || temp[4] || temp[6] || temp[8];

      if (id) {
        if (!file) {
          var svgObj = svg.getElementById(id);

          if (svgObj) {
            if (this.stack.indexOf(svgObj) === -1) {
              return svgObj;
            } else {
              warningCallback('SVGtoPDF: loop of circular references for id "' + id + '"');
              return;
            }
          }
        }

        if (documentCallback) {
          var svgs = documentCache[file];

          if (!svgs) {
            svgs = documentCallback(file);

            if (!isArrayLike(svgs)) {
              svgs = [svgs];
            }

            for (var i = 0; i < svgs.length; i++) {
              if (typeof svgs[i] === 'string') {
                svgs[i] = parseXml(svgs[i]);
              }
            }

            documentCache[file] = svgs;
          }

          for (var _i7 = 0; _i7 < svgs.length; _i7++) {
            var _svgObj = svgs[_i7].getElementById(id);

            if (_svgObj) {
              if (this.stack.indexOf(_svgObj) === -1) {
                return _svgObj;
              } else {
                warningCallback('SVGtoPDF: loop of circular references for id "' + file + '#' + id + '"');
                return;
              }
            }
          }
        }
      }
    };

    this.computeUnits = function (value, unit, percent, isFontSize) {
      if (unit === '%') {
        return parseFloat(value) / 100 * (isFontSize || percent != null ? percent : this.getViewport());
      } else if (unit === 'ex' || unit === 'em') {
        return value * {
          'em': 1,
          'ex': 0.5
        }[unit] * (isFontSize ? percent : this.get('font-size'));
      } else {
        return value * {
          '': 1,
          'px': 1,
          'pt': 96 / 72,
          'cm': 96 / 2.54,
          'mm': 96 / 25.4,
          'in': 96,
          'pc': 96 / 6
        }[unit];
      }
    };

    this.computeLength = function (value, percent, initial, isFontSize) {
      var parser = new StringParser((value || '').trim()),
          temp1,
          temp2;

      if (typeof (temp1 = parser.matchNumber()) === 'string' && typeof (temp2 = parser.matchLengthUnit()) === 'string' && !parser.matchAll()) {
        return this.computeUnits(temp1, temp2, percent, isFontSize);
      }

      return initial;
    };

    this.computeLengthList = function (value, percent, strict) {
      var parser = new StringParser((value || '').trim()),
          result = [],
          temp1,
          temp2;

      while (typeof (temp1 = parser.matchNumber()) === 'string' && typeof (temp2 = parser.matchLengthUnit()) === 'string') {
        result.push(this.computeUnits(temp1, temp2, percent));
        parser.matchSeparator();
      }

      if (strict && parser.matchAll()) {
        return;
      }

      return result;
    };

    this.getLength = function (key, percent, initial) {
      return this.computeLength(this.attr(key), percent, initial);
    };

    this.getLengthList = function (key, percent) {
      return this.computeLengthList(this.attr(key), percent);
    };

    this.getUrl = function (key) {
      return this.resolveUrl(this.attr(key));
    };

    this.getNumberList = function (key) {
      var parser = new StringParser((this.attr(key) || '').trim()),
          result = [],
          temp;

      while (temp = parser.matchNumber()) {
        result.push(Number(temp));
        parser.matchSeparator();
      }

      result.error = parser.matchAll();
      return result;
    };

    this.getViewbox = function (key, initial) {
      var viewBox = this.getNumberList(key);

      if (viewBox.length === 4 && viewBox[2] >= 0 && viewBox[3] >= 0) {
        return viewBox;
      }

      return initial;
    };

    this.getPercent = function (key, initial) {
      var value = this.attr(key);
      var parser = new StringParser((value || '').trim()),
          temp1,
          temp2;
      var number = parser.matchNumber();

      if (!number) {
        return initial;
      }

      if (parser.match('%')) {
        number *= 0.01;
      }

      if (parser.matchAll()) {
        return initial;
      }

      return Math.max(0, Math.min(1, number));
    };

    this.chooseValue = function (args) {
      for (var i = 0; i < arguments.length; i++) {
        if (arguments[i] != null && arguments[i] === arguments[i]) {
          return arguments[i];
        }
      }

      return arguments[arguments.length - 1];
    };

    this.get = function (key) {
      if (styleCache[key] !== undefined) {
        return styleCache[key];
      }

      var keyInfo = Properties[key] || {},
          value,
          result;

      for (var i = 0; i < 3; i++) {
        switch (i) {
          case 0:
            if (key !== 'transform') {
              // the CSS transform behaves strangely
              value = this.css[keyInfo.css || key];
            }

            break;

          case 1:
            value = this.style[key];
            break;

          case 2:
            value = this.attr(key);
            break;
        }

        if (value === 'inherit') {
          result = this.inherits ? this.inherits.get(key) : keyInfo.initial;

          if (result != null) {
            return styleCache[key] = result;
          }
        }

        if (keyInfo.values != null) {
          result = keyInfo.values[value];

          if (result != null) {
            return styleCache[key] = result;
          }
        }

        if (value != null) {
          var parsed = void 0;

          switch (key) {
            case 'font-size':
              result = this.computeLength(value, this.inherits ? this.inherits.get(key) : keyInfo.initial, undefined, true);
              break;

            case 'baseline-shift':
              result = this.computeLength(value, this.get('font-size'));
              break;

            case 'font-family':
              result = value || undefined;
              break;

            case 'opacity':
            case 'stroke-opacity':
            case 'fill-opacity':
            case 'stop-opacity':
              parsed = parseFloat(value);

              if (!isNaN(parsed)) {
                result = Math.max(0, Math.min(1, parsed));
              }

              break;

            case 'transform':
              result = parseTranform(value);
              break;

            case 'stroke-dasharray':
              if (value === 'none') {
                result = [];
              } else if (parsed = this.computeLengthList(value, this.getViewport(), true)) {
                var sum = 0,
                    error = false;

                for (var j = 0; j < parsed.length; j++) {
                  if (parsed[j] < 0) {
                    error = true;
                  }

                  sum += parsed[j];
                }

                if (!error) {
                  if (parsed.length % 2 === 1) {
                    parsed = parsed.concat(parsed);
                  }

                  result = sum === 0 ? [] : parsed;
                }
              }

              break;

            case 'color':
              if (value === 'none' || value === 'transparent') {
                result = 'none';
              } else {
                result = parseColor(value);
              }

              break;

            case 'fill':
            case 'stroke':
              if (value === 'none' || value === 'transparent') {
                result = 'none';
              } else if (value === 'currentColor') {
                result = this.get('color');
              } else if (parsed = parseColor(value)) {
                return parsed;
              } else if (parsed = (value || '').split(' ')) {
                var object = this.resolveUrl(parsed[0]),
                    fallbackColor = parseColor(parsed[1]);

                if (object == null) {
                  result = fallbackColor;
                } else if (object.nodeName === 'linearGradient' || object.nodeName === 'radialGradient') {
                  result = new SvgElemGradient(object, null, fallbackColor);
                } else if (object.nodeName === 'pattern') {
                  result = new SvgElemPattern(object, null, fallbackColor);
                } else {
                  result = fallbackColor;
                }
              }

              break;

            case 'stop-color':
              if (value === 'none' || value === 'transparent') {
                result = 'none';
              } else if (value === 'currentColor') {
                result = this.get('color');
              } else {
                result = parseColor(value);
              }

              break;

            case 'marker-start':
            case 'marker-mid':
            case 'marker-end':
            case 'clip-path':
            case 'mask':
              if (value === 'none') {
                result = 'none';
              } else {
                result = this.resolveUrl(value);
              }

              break;

            case 'stroke-width':
              parsed = this.computeLength(value, this.getViewport());

              if (parsed != null && parsed >= 0) {
                result = parsed;
              }

              break;

            case 'stroke-miterlimit':
              parsed = parseFloat(value);

              if (parsed != null && parsed >= 1) {
                result = parsed;
              }

              break;

            case 'word-spacing':
            case 'letter-spacing':
              result = this.computeLength(value, this.getViewport());
              break;

            case 'stroke-dashoffset':
              result = this.computeLength(value, this.getViewport());

              if (result != null) {
                if (result < 0) {
                  // fix for crbug.com/660850
                  var dasharray = this.get('stroke-dasharray');

                  for (var _j = 0; _j < dasharray.length; _j++) {
                    result += dasharray[_j];
                  }
                }
              }

              break;
          }

          if (result != null) {
            return styleCache[key] = result;
          }
        }
      }

      return styleCache[key] = keyInfo.inherit && this.inherits ? this.inherits.get(key) : keyInfo.initial;
    };

    this.getChildren = function () {
      if (childrenCache != null) {
        return childrenCache;
      }

      var children = [];

      for (var i = 0; i < obj.childNodes.length; i++) {
        var child = obj.childNodes[i];

        if (!child.error && this.allowedChildren.indexOf(child.nodeName) !== -1) {
          children.push(createSVGElement(child, this));
        }
      }

      return childrenCache = children;
    };

    this.getParentVWidth = function () {
      return this.inherits ? this.inherits.getVWidth() : viewportWidth;
    };

    this.getParentVHeight = function () {
      return this.inherits ? this.inherits.getVHeight() : viewportHeight;
    };

    this.getParentViewport = function () {
      return Math.sqrt(0.5 * this.getParentVWidth() * this.getParentVWidth() + 0.5 * this.getParentVHeight() * this.getParentVHeight());
    };

    this.getVWidth = function () {
      return this.getParentVWidth();
    };

    this.getVHeight = function () {
      return this.getParentVHeight();
    };

    this.getViewport = function () {
      return Math.sqrt(0.5 * this.getVWidth() * this.getVWidth() + 0.5 * this.getVHeight() * this.getVHeight());
    };

    this.getBoundingBox = function () {
      var shape = this.getBoundingShape();
      return shape.getBoundingBox();
    };
  };

  var SvgElemStylable = function SvgElemStylable(obj, inherits) {
    SvgElem.call(this, obj, inherits);

    this.transform = function () {
      doc.transform.apply(doc, this.getTransformation());
    };

    this.clip = function () {
      if (this.get('clip-path') !== 'none') {
        var clipPath = new SvgElemClipPath(this.get('clip-path'), null);
        clipPath.useMask(this.getBoundingBox());
        return true;
      }
    };

    this.mask = function () {
      if (this.get('mask') !== 'none') {
        var mask = new SvgElemMask(this.get('mask'), null);
        mask.useMask(this.getBoundingBox());
        return true;
      }
    };

    this.getFill = function (isClip, isMask) {
      var opacity = this.get('opacity'),
          fill = this.get('fill'),
          fillOpacity = this.get('fill-opacity');

      if (isClip) {
        return DefaultColors.white;
      }

      if (fill !== 'none' && opacity && fillOpacity) {
        if (fill instanceof SvgElemGradient || fill instanceof SvgElemPattern) {
          return fill.getPaint(this.getBoundingBox(), fillOpacity * opacity, isClip, isMask);
        }

        return opacityToColor(fill, fillOpacity * opacity, isMask);
      }
    };

    this.getStroke = function (isClip, isMask) {
      var opacity = this.get('opacity'),
          stroke = this.get('stroke'),
          strokeOpacity = this.get('stroke-opacity');

      if (isClip || isEqual(this.get('stroke-width'), 0)) {
        return;
      }

      if (stroke !== 'none' && opacity && strokeOpacity) {
        if (stroke instanceof SvgElemGradient || stroke instanceof SvgElemPattern) {
          return stroke.getPaint(this.getBoundingBox(), strokeOpacity * opacity, isClip, isMask);
        }

        return opacityToColor(stroke, strokeOpacity * opacity, isMask);
      }
    };
  };

  var SvgElemHasChildren = function SvgElemHasChildren(obj, inherits) {
    SvgElemStylable.call(this, obj, inherits);
    this.allowedChildren = ['use', 'g', 'a', 'svg', 'image', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'path', 'text'];

    this.getBoundingShape = function () {
      var shape = new SvgShape(),
          children = this.getChildren();

      for (var i = 0; i < children.length; i++) {
        if (children[i].get('display') !== 'none') {
          if (typeof children[i].getBoundingShape === 'function') {
            var childShape = children[i].getBoundingShape().clone();

            if (typeof children[i].getTransformation === 'function') {
              childShape.transform(children[i].getTransformation());
            }

            shape.mergeShape(childShape);
          }
        }
      }

      return shape;
    };

    this.drawChildren = function (isClip, isMask) {
      var children = this.getChildren();

      for (var i = 0; i < children.length; i++) {
        if (children[i].get('display') !== 'none') {
          if (typeof children[i].drawInDocument === 'function') {
            children[i].drawInDocument(isClip, isMask);
          }
        }
      }
    };
  };

  var SvgElemContainer = function SvgElemContainer(obj, inherits) {
    SvgElemHasChildren.call(this, obj, inherits);

    this.drawContent = function (isClip, isMask) {
      this.transform();
      var clipped = this.clip(),
          masked = this.mask(),
          group;

      if ((this.get('opacity') < 1 || clipped || masked) && !isClip) {
        group = docBeginGroup(getPageBBox());
      }

      this.drawChildren(isClip, isMask);

      if (group) {
        docEndGroup(group);
        doc.fillOpacity(this.get('opacity'));
        docInsertGroup(group);
      }
    };
  };

  var SvgElemUse = function SvgElemUse(obj, inherits) {
    SvgElemContainer.call(this, obj, inherits);
    var x = this.getLength('x', this.getVWidth(), 0),
        y = this.getLength('y', this.getVHeight(), 0),
        child = this.getUrl('href') || this.getUrl('xlink:href');

    if (child) {
      child = createSVGElement(child, this);
    }

    this.getChildren = function () {
      return child ? [child] : [];
    };

    this.drawInDocument = function (isClip, isMask) {
      doc.save();
      this.drawContent(isClip, isMask);
      doc.restore();
    };

    this.getTransformation = function () {
      return multiplyMatrix(this.get('transform'), [1, 0, 0, 1, x, y]);
    };
  };

  var SvgElemSymbol = function SvgElemSymbol(obj, inherits) {
    SvgElemContainer.call(this, obj, inherits);
    var width = this.getLength('width', this.getParentVWidth(), this.getParentVWidth()),
        height = this.getLength('height', this.getParentVHeight(), this.getParentVHeight());

    if (inherits instanceof SvgElemUse) {
      width = inherits.getLength('width', inherits.getParentVWidth(), width);
      height = inherits.getLength('height', inherits.getParentVHeight(), height);
    }

    var aspectRatio = (this.attr('preserveAspectRatio') || '').trim(),
        viewBox = this.getViewbox('viewBox', [0, 0, width, height]);

    this.getVWidth = function () {
      return viewBox[2];
    };

    this.getVHeight = function () {
      return viewBox[3];
    };

    this.drawInDocument = function (isClip, isMask) {
      doc.save();
      this.drawContent(isClip, isMask);
      doc.restore();
    };

    this.getTransformation = function () {
      return multiplyMatrix(parseAspectRatio(aspectRatio, width, height, viewBox[2], viewBox[3]), [1, 0, 0, 1, -viewBox[0], -viewBox[1]]);
    };
  };

  var SvgElemGroup = function SvgElemGroup(obj, inherits) {
    SvgElemContainer.call(this, obj, inherits);

    this.drawInDocument = function (isClip, isMask) {
      doc.save();

      if (this.link && !isClip && !isMask) {
        this.addLink();
      }

      this.drawContent(isClip, isMask);
      doc.restore();
    };

    this.getTransformation = function () {
      return this.get('transform');
    };
  };

  var SvgElemLink = function SvgElemLink(obj, inherits) {
    if (inherits && inherits.isText) {
      SvgElemTspan.call(this, obj, inherits);
      this.allowedChildren = ['textPath', 'tspan', '#text', '#cdata-section', 'a'];
    } else {
      SvgElemGroup.call(this, obj, inherits);
    }

    this.link = this.attr('href') || this.attr('xlink:href');

    this.addLink = function () {
      if (this.link.match(/^(?:[a-z][a-z0-9+.-]*:|\/\/)?/i) && this.getChildren().length) {
        var bbox = this.getBoundingShape().transform(getGlobalMatrix()).getBoundingBox();
        docInsertLink(bbox[0], bbox[1], bbox[2], bbox[3], this.link);
      }
    };
  };

  var SvgElemSvg = function SvgElemSvg(obj, inherits) {
    SvgElemContainer.call(this, obj, inherits);
    var width = this.getLength('width', this.getParentVWidth(), this.getParentVWidth()),
        height = this.getLength('height', this.getParentVHeight(), this.getParentVHeight()),
        x = this.getLength('x', this.getParentVWidth(), 0),
        y = this.getLength('y', this.getParentVHeight(), 0);

    if (inherits instanceof SvgElemUse) {
      width = inherits.getLength('width', inherits.getParentVWidth(), width);
      height = inherits.getLength('height', inherits.getParentVHeight(), height);
    }

    var aspectRatio = this.attr('preserveAspectRatio'),
        viewBox = this.getViewbox('viewBox', [0, 0, width, height]);

    if (this.isOuterElement && preserveAspectRatio) {
      x = y = 0;
      width = viewportWidth;
      height = viewportHeight;
      aspectRatio = preserveAspectRatio;
    }

    this.getVWidth = function () {
      return viewBox[2];
    };

    this.getVHeight = function () {
      return viewBox[3];
    };

    this.drawInDocument = function (isClip, isMask) {
      doc.save();

      if (this.get('overflow') === 'hidden') {
        new SvgShape().M(x, y).L(x + width, y).L(x + width, y + height).L(x, y + height).Z().transform(this.get('transform')).insertInDocument();
        doc.clip();
      }

      this.drawContent(isClip, isMask);
      doc.restore();
    };

    this.getTransformation = function () {
      return multiplyMatrix(this.get('transform'), [1, 0, 0, 1, x, y], parseAspectRatio(aspectRatio, width, height, viewBox[2], viewBox[3]), [1, 0, 0, 1, -viewBox[0], -viewBox[1]]);
    };
  };

  var SVGElemImage = function SVGElemImage(obj, inherits) {
    SvgElemStylable.call(this, obj, inherits);
    var link = imageCallback(this.attr('href') || this.attr('xlink:href') || ''),
        x = this.getLength('x', this.getVWidth(), 0),
        y = this.getLength('y', this.getVHeight(), 0),
        width = this.getLength('width', this.getVWidth(), 'auto'),
        height = this.getLength('height', this.getVHeight(), 'auto'),
        image;

    try {
      image = doc.openImage(link);
    } catch (e) {
      warningCallback('SVGElemImage: failed to open image "' + link + '" in PDFKit');
    }

    if (image) {
      if (width === 'auto' && height !== 'auto') {
        width = height * image.width / image.height;
      } else if (height === 'auto' && width !== 'auto') {
        height = width * image.height / image.width;
      } else if (width === 'auto' && height === 'auto') {
        width = image.width;
        height = image.height;
      }
    }

    if (width === 'auto' || width < 0) {
      width = 0;
    }

    if (height === 'auto' || height < 0) {
      height = 0;
    }

    this.getTransformation = function () {
      return this.get('transform');
    };

    this.getBoundingShape = function () {
      return new SvgShape().M(x, y).L(x + width, y).M(x + width, y + height).L(x, y + height);
    };

    this.drawInDocument = function (isClip, isMask) {
      if (this.get('visibility') === 'hidden' || !image) {
        return;
      }

      doc.save();
      this.transform();

      if (this.get('overflow') === 'hidden') {
        doc.rect(x, y, width, height).clip();
      }

      this.clip();
      this.mask();
      doc.translate(x, y);
      doc.transform.apply(doc, parseAspectRatio(this.attr('preserveAspectRatio'), width, height, image ? image.width : width, image ? image.height : height));

      if (!isClip) {
        doc.fillOpacity(this.get('opacity'));
        doc.image(image, 0, 0);
      } else {
        doc.rect(0, 0, image.width, image.height);
        docFillColor(DefaultColors.white).fill();
      }

      doc.restore();
    };
  };

  var SvgElemPattern = function SvgElemPattern(obj, inherits, fallback) {
    SvgElemHasChildren.call(this, obj, inherits);

    this.ref = function () {
      var ref = this.getUrl('href') || this.getUrl('xlink:href');

      if (ref && ref.nodeName === obj.nodeName) {
        return new SvgElemPattern(ref, inherits, fallback);
      }
    }.call(this);

    var _attr = this.attr;

    this.attr = function (key) {
      var attr = _attr.call(this, key);

      if (attr != null || key === 'href' || key === 'xlink:href') {
        return attr;
      }

      return this.ref ? this.ref.attr(key) : null;
    };

    var _getChildren = this.getChildren;

    this.getChildren = function () {
      var children = _getChildren.call(this);

      if (children.length > 0) {
        return children;
      }

      return this.ref ? this.ref.getChildren() : [];
    };

    this.getPaint = function (bBox, gOpacity, isClip, isMask) {
      var bBoxUnitsPattern = this.attr('patternUnits') !== 'userSpaceOnUse',
          bBoxUnitsContent = this.attr('patternContentUnits') === 'objectBoundingBox',
          x = this.getLength('x', bBoxUnitsPattern ? 1 : this.getParentVWidth(), 0),
          y = this.getLength('y', bBoxUnitsPattern ? 1 : this.getParentVHeight(), 0),
          width = this.getLength('width', bBoxUnitsPattern ? 1 : this.getParentVWidth(), 0),
          height = this.getLength('height', bBoxUnitsPattern ? 1 : this.getParentVHeight(), 0);

      if (bBoxUnitsContent && !bBoxUnitsPattern) {
        // Use the same units for pattern & pattern content
        x = (x - bBox[0]) / (bBox[2] - bBox[0]) || 0;
        y = (y - bBox[1]) / (bBox[3] - bBox[1]) || 0;
        width = width / (bBox[2] - bBox[0]) || 0;
        height = height / (bBox[3] - bBox[1]) || 0;
      } else if (!bBoxUnitsContent && bBoxUnitsPattern) {
        x = bBox[0] + x * (bBox[2] - bBox[0]);
        y = bBox[1] + y * (bBox[3] - bBox[1]);
        width = width * (bBox[2] - bBox[0]);
        height = height * (bBox[3] - bBox[1]);
      }

      var viewBox = this.getViewbox('viewBox', [0, 0, width, height]),
          aspectRatio = (this.attr('preserveAspectRatio') || '').trim(),
          aspectRatioMatrix = multiplyMatrix(parseAspectRatio(aspectRatio, width, height, viewBox[2], viewBox[3], 0), [1, 0, 0, 1, -viewBox[0], -viewBox[1]]),
          matrix = parseTranform(this.attr('patternTransform'));

      if (bBoxUnitsContent) {
        matrix = multiplyMatrix([bBox[2] - bBox[0], 0, 0, bBox[3] - bBox[1], bBox[0], bBox[1]], matrix);
      }

      matrix = multiplyMatrix(matrix, [1, 0, 0, 1, x, y]);

      if ((matrix = validateMatrix(matrix)) && (aspectRatioMatrix = validateMatrix(aspectRatioMatrix)) && (width = validateNumber(width)) && (height = validateNumber(height))) {
        var group = docBeginGroup([0, 0, width, height]);
        doc.transform.apply(doc, aspectRatioMatrix);
        this.drawChildren(isClip, isMask);
        docEndGroup(group);
        return [docCreatePattern(group, width, height, matrix), gOpacity];
      } else {
        return fallback ? [fallback[0], fallback[1] * gOpacity] : undefined;
      }
    };

    this.getVWidth = function () {
      var bBoxUnitsPattern = this.attr('patternUnits') !== 'userSpaceOnUse',
          width = this.getLength('width', bBoxUnitsPattern ? 1 : this.getParentVWidth(), 0);
      return this.getViewbox('viewBox', [0, 0, width, 0])[2];
    };

    this.getVHeight = function () {
      var bBoxUnitsPattern = this.attr('patternUnits') !== 'userSpaceOnUse',
          height = this.getLength('height', bBoxUnitsPattern ? 1 : this.getParentVHeight(), 0);
      return this.getViewbox('viewBox', [0, 0, 0, height])[3];
    };
  };

  var SvgElemGradient = function SvgElemGradient(obj, inherits, fallback) {
    SvgElem.call(this, obj, inherits);
    this.allowedChildren = ['stop'];

    this.ref = function () {
      var ref = this.getUrl('href') || this.getUrl('xlink:href');

      if (ref && ref.nodeName === obj.nodeName) {
        return new SvgElemGradient(ref, inherits, fallback);
      }
    }.call(this);

    var _attr = this.attr;

    this.attr = function (key) {
      var attr = _attr.call(this, key);

      if (attr != null || key === 'href' || key === 'xlink:href') {
        return attr;
      }

      return this.ref ? this.ref.attr(key) : null;
    };

    var _getChildren = this.getChildren;

    this.getChildren = function () {
      var children = _getChildren.call(this);

      if (children.length > 0) {
        return children;
      }

      return this.ref ? this.ref.getChildren() : [];
    };

    this.getPaint = function (bBox, gOpacity, isClip, isMask) {
      var children = this.getChildren();

      if (children.length === 0) {
        return;
      }

      if (children.length === 1) {
        var child = children[0],
            stopColor = child.get('stop-color');

        if (stopColor === 'none') {
          return;
        }

        return opacityToColor(stopColor, child.get('stop-opacity') * gOpacity, isMask);
      }

      var bBoxUnits = this.attr('gradientUnits') !== 'userSpaceOnUse',
          matrix = parseTranform(this.attr('gradientTransform')),
          spread = this.attr('spreadMethod'),
          grad,
          x1,
          x2,
          y1,
          y2,
          r2,
          nAfter = 0,
          nBefore = 0,
          nTotal = 1;

      if (bBoxUnits) {
        matrix = multiplyMatrix([bBox[2] - bBox[0], 0, 0, bBox[3] - bBox[1], bBox[0], bBox[1]], matrix);
      }

      if (matrix = validateMatrix(matrix)) {
        if (this.name === 'linearGradient') {
          x1 = this.getLength('x1', bBoxUnits ? 1 : this.getVWidth(), 0);
          x2 = this.getLength('x2', bBoxUnits ? 1 : this.getVWidth(), bBoxUnits ? 1 : this.getVWidth());
          y1 = this.getLength('y1', bBoxUnits ? 1 : this.getVHeight(), 0);
          y2 = this.getLength('y2', bBoxUnits ? 1 : this.getVHeight(), 0);
        } else {
          x2 = this.getLength('cx', bBoxUnits ? 1 : this.getVWidth(), bBoxUnits ? 0.5 : 0.5 * this.getVWidth());
          y2 = this.getLength('cy', bBoxUnits ? 1 : this.getVHeight(), bBoxUnits ? 0.5 : 0.5 * this.getVHeight());
          r2 = this.getLength('r', bBoxUnits ? 1 : this.getViewport(), bBoxUnits ? 0.5 : 0.5 * this.getViewport());
          x1 = this.getLength('fx', bBoxUnits ? 1 : this.getVWidth(), x2);
          y1 = this.getLength('fy', bBoxUnits ? 1 : this.getVHeight(), y2);

          if (r2 < 0) {
            warningCallback('SvgElemGradient: negative r value');
          }

          var d = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2)),
              multiplier = 1;

          if (d > r2) {
            // according to specification
            multiplier = r2 / d;
            x1 = x2 + (x1 - x2) * multiplier;
            y1 = y2 + (y1 - y2) * multiplier;
          }

          r2 = Math.max(r2, d * multiplier * (1 + 1e-6)); // fix for edge-case gradients see issue #84
        }

        if (spread === 'reflect' || spread === 'repeat') {
          var inv = inverseMatrix(matrix),
              corner1 = transformPoint([bBox[0], bBox[1]], inv),
              corner2 = transformPoint([bBox[2], bBox[1]], inv),
              corner3 = transformPoint([bBox[2], bBox[3]], inv),
              corner4 = transformPoint([bBox[0], bBox[3]], inv);

          if (this.name === 'linearGradient') {
            // See file 'gradient-repeat-maths.png'
            nAfter = Math.max((corner1[0] - x2) * (x2 - x1) + (corner1[1] - y2) * (y2 - y1), (corner2[0] - x2) * (x2 - x1) + (corner2[1] - y2) * (y2 - y1), (corner3[0] - x2) * (x2 - x1) + (corner3[1] - y2) * (y2 - y1), (corner4[0] - x2) * (x2 - x1) + (corner4[1] - y2) * (y2 - y1)) / (Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
            nBefore = Math.max((corner1[0] - x1) * (x1 - x2) + (corner1[1] - y1) * (y1 - y2), (corner2[0] - x1) * (x1 - x2) + (corner2[1] - y1) * (y1 - y2), (corner3[0] - x1) * (x1 - x2) + (corner3[1] - y1) * (y1 - y2), (corner4[0] - x1) * (x1 - x2) + (corner4[1] - y1) * (y1 - y2)) / (Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
          } else {
            nAfter = Math.sqrt(Math.max(Math.pow(corner1[0] - x2, 2) + Math.pow(corner1[1] - y2, 2), Math.pow(corner2[0] - x2, 2) + Math.pow(corner2[1] - y2, 2), Math.pow(corner3[0] - x2, 2) + Math.pow(corner3[1] - y2, 2), Math.pow(corner4[0] - x2, 2) + Math.pow(corner4[1] - y2, 2))) / r2 - 1;
          }

          nAfter = Math.ceil(nAfter + 0.5); // Add a little more because the stroke can extend outside of the bounding box

          nBefore = Math.ceil(nBefore + 0.5);
          nTotal = nBefore + 1 + nAfter; // How many times the gradient needs to be repeated to fill the object bounding box
        }

        if (this.name === 'linearGradient') {
          grad = doc.linearGradient(x1 - nBefore * (x2 - x1), y1 - nBefore * (y2 - y1), x2 + nAfter * (x2 - x1), y2 + nAfter * (y2 - y1));
        } else {
          grad = doc.radialGradient(x1, y1, 0, x2, y2, r2 + nAfter * r2);
        }

        for (var n = 0; n < nTotal; n++) {
          var offset = 0,
              inOrder = spread !== 'reflect' || (n - nBefore) % 2 === 0;

          for (var i = 0; i < children.length; i++) {
            var _child = children[inOrder ? i : children.length - 1 - i],
                _stopColor = _child.get('stop-color');

            if (_stopColor === 'none') {
              _stopColor = DefaultColors.transparent;
            }

            _stopColor = opacityToColor(_stopColor, _child.get('stop-opacity') * gOpacity, isMask);
            offset = Math.max(offset, inOrder ? _child.getPercent('offset', 0) : 1 - _child.getPercent('offset', 0));

            if (i === 0 && _stopColor[0].length === 4) {
              grad._colorSpace = 'DeviceCMYK';
            } // Fix until PR #763 is merged into PDFKit


            if (i === 0 && offset > 0) {
              grad.stop((n + 0) / nTotal, _stopColor[0], _stopColor[1]);
            }

            grad.stop((n + offset) / (nAfter + nBefore + 1), _stopColor[0], _stopColor[1]);

            if (i === children.length - 1 && offset < 1) {
              grad.stop((n + 1) / nTotal, _stopColor[0], _stopColor[1]);
            }
          }
        }

        grad.setTransform.apply(grad, matrix);
        return [grad, 1];
      } else {
        return fallback ? [fallback[0], fallback[1] * gOpacity] : undefined;
      }
    };
  };

  var SvgElemBasicShape = function SvgElemBasicShape(obj, inherits) {
    SvgElemStylable.call(this, obj, inherits);
    this.dashScale = 1;

    this.getBoundingShape = function () {
      return this.shape;
    };

    this.getTransformation = function () {
      return this.get('transform');
    };

    this.drawInDocument = function (isClip, isMask) {
      if (this.get('visibility') === 'hidden' || !this.shape) {
        return;
      }

      doc.save();
      this.transform();
      this.clip();

      if (!isClip) {
        var masked = this.mask(),
            group;

        if (masked) {
          group = docBeginGroup(getPageBBox());
        }

        var subPaths = this.shape.getSubPaths(),
            fill = this.getFill(isClip, isMask),
            stroke = this.getStroke(isClip, isMask),
            lineWidth = this.get('stroke-width'),
            lineCap = this.get('stroke-linecap');

        if (fill || stroke) {
          if (fill) {
            docFillColor(fill);
          }

          if (stroke) {
            for (var j = 0; j < subPaths.length; j++) {
              if (isEqual(subPaths[j].totalLength, 0)) {
                if ((lineCap === 'square' || lineCap === 'round') && lineWidth > 0) {
                  if (subPaths[j].startPoint && subPaths[j].startPoint.length > 1) {
                    var _x4 = subPaths[j].startPoint[0],
                        _y4 = subPaths[j].startPoint[1];
                    docFillColor(stroke);

                    if (lineCap === 'square') {
                      doc.rect(_x4 - 0.5 * lineWidth, _y4 - 0.5 * lineWidth, lineWidth, lineWidth);
                    } else if (lineCap === 'round') {
                      doc.circle(_x4, _y4, 0.5 * lineWidth);
                    }

                    doc.fill();
                  }
                }
              }
            }

            var dashArray = this.get('stroke-dasharray'),
                dashOffset = this.get('stroke-dashoffset');

            if (isNotEqual(this.dashScale, 1)) {
              for (var _j2 = 0; _j2 < dashArray.length; _j2++) {
                dashArray[_j2] *= this.dashScale;
              }

              dashOffset *= this.dashScale;
            }

            docStrokeColor(stroke);
            doc.lineWidth(lineWidth).miterLimit(this.get('stroke-miterlimit')).lineJoin(this.get('stroke-linejoin')).lineCap(lineCap).dash(dashArray, {
              phase: dashOffset
            });
          }

          for (var _j3 = 0; _j3 < subPaths.length; _j3++) {
            if (subPaths[_j3].totalLength > 0) {
              subPaths[_j3].insertInDocument();
            }
          }

          if (fill && stroke) {
            doc.fillAndStroke(this.get('fill-rule'));
          } else if (fill) {
            doc.fill(this.get('fill-rule'));
          } else if (stroke) {
            doc.stroke();
          }
        }

        var markerStart = this.get('marker-start'),
            markerMid = this.get('marker-mid'),
            markerEnd = this.get('marker-end');

        if (markerStart !== 'none' || markerMid !== 'none' || markerEnd !== 'none') {
          var markersPos = this.shape.getMarkers();

          if (markerStart !== 'none') {
            var marker = new SvgElemMarker(markerStart, null);
            marker.drawMarker(false, isMask, markersPos[0], lineWidth);
          }

          if (markerMid !== 'none') {
            for (var i = 1; i < markersPos.length - 1; i++) {
              var _marker = new SvgElemMarker(markerMid, null);

              _marker.drawMarker(false, isMask, markersPos[i], lineWidth);
            }
          }

          if (markerEnd !== 'none') {
            var _marker2 = new SvgElemMarker(markerEnd, null);

            _marker2.drawMarker(false, isMask, markersPos[markersPos.length - 1], lineWidth);
          }
        }

        if (group) {
          docEndGroup(group);
          docInsertGroup(group);
        }
      } else {
        this.shape.insertInDocument();
        docFillColor(DefaultColors.white);
        doc.fill(this.get('clip-rule'));
      }

      doc.restore();
    };
  };

  var SvgElemRect = function SvgElemRect(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var x = this.getLength('x', this.getVWidth(), 0),
        y = this.getLength('y', this.getVHeight(), 0),
        w = this.getLength('width', this.getVWidth(), 0),
        h = this.getLength('height', this.getVHeight(), 0),
        rx = this.getLength('rx', this.getVWidth()),
        ry = this.getLength('ry', this.getVHeight());

    if (rx === undefined && ry === undefined) {
      rx = ry = 0;
    } else if (rx === undefined && ry !== undefined) {
      rx = ry;
    } else if (rx !== undefined && ry === undefined) {
      ry = rx;
    }

    if (w > 0 && h > 0) {
      if (rx && ry) {
        rx = Math.min(rx, 0.5 * w);
        ry = Math.min(ry, 0.5 * h);
        this.shape = new SvgShape().M(x + rx, y).L(x + w - rx, y).A(rx, ry, 0, 0, 1, x + w, y + ry).L(x + w, y + h - ry).A(rx, ry, 0, 0, 1, x + w - rx, y + h).L(x + rx, y + h).A(rx, ry, 0, 0, 1, x, y + h - ry).L(x, y + ry).A(rx, ry, 0, 0, 1, x + rx, y).Z();
      } else {
        this.shape = new SvgShape().M(x, y).L(x + w, y).L(x + w, y + h).L(x, y + h).Z();
      }
    } else {
      this.shape = new SvgShape();
    }
  };

  var SvgElemCircle = function SvgElemCircle(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var cx = this.getLength('cx', this.getVWidth(), 0),
        cy = this.getLength('cy', this.getVHeight(), 0),
        r = this.getLength('r', this.getViewport(), 0);

    if (r > 0) {
      this.shape = new SvgShape().M(cx + r, cy).A(r, r, 0, 0, 1, cx - r, cy).A(r, r, 0, 0, 1, cx + r, cy).Z();
    } else {
      this.shape = new SvgShape();
    }
  };

  var SvgElemEllipse = function SvgElemEllipse(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var cx = this.getLength('cx', this.getVWidth(), 0),
        cy = this.getLength('cy', this.getVHeight(), 0),
        rx = this.getLength('rx', this.getVWidth(), 0),
        ry = this.getLength('ry', this.getVHeight(), 0);

    if (rx > 0 && ry > 0) {
      this.shape = new SvgShape().M(cx + rx, cy).A(rx, ry, 0, 0, 1, cx - rx, cy).A(rx, ry, 0, 0, 1, cx + rx, cy).Z();
    } else {
      this.shape = new SvgShape();
    }
  };

  var SvgElemLine = function SvgElemLine(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var x1 = this.getLength('x1', this.getVWidth(), 0),
        y1 = this.getLength('y1', this.getVHeight(), 0),
        x2 = this.getLength('x2', this.getVWidth(), 0),
        y2 = this.getLength('y2', this.getVHeight(), 0);
    this.shape = new SvgShape().M(x1, y1).L(x2, y2);
  };

  var SvgElemPolyline = function SvgElemPolyline(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var points = this.getNumberList('points');
    this.shape = new SvgShape();

    for (var i = 0; i < points.length - 1; i += 2) {
      if (i === 0) {
        this.shape.M(points[i], points[i + 1]);
      } else {
        this.shape.L(points[i], points[i + 1]);
      }
    }

    if (points.error) {
      warningCallback('SvgElemPolygon: unexpected string ' + points.error);
    }

    if (points.length % 2 === 1) {
      warningCallback('SvgElemPolyline: uneven number of coordinates');
    }
  };

  var SvgElemPolygon = function SvgElemPolygon(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var points = this.getNumberList('points');
    this.shape = new SvgShape();

    for (var i = 0; i < points.length - 1; i += 2) {
      if (i === 0) {
        this.shape.M(points[i], points[i + 1]);
      } else {
        this.shape.L(points[i], points[i + 1]);
      }
    }

    this.shape.Z();

    if (points.error) {
      warningCallback('SvgElemPolygon: unexpected string ' + points.error);
    }

    if (points.length % 2 === 1) {
      warningCallback('SvgElemPolygon: uneven number of coordinates');
    }
  };

  var SvgElemPath = function SvgElemPath(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    this.shape = new SvgShape().path(this.attr('d'));
    var pathLength = this.getLength('pathLength', this.getViewport());
    this.pathLength = pathLength > 0 ? pathLength : undefined;
    this.dashScale = this.pathLength !== undefined ? this.shape.totalLength / this.pathLength : 1;
  };

  var SvgElemMarker = function SvgElemMarker(obj, inherits) {
    SvgElemHasChildren.call(this, obj, inherits);
    var width = this.getLength('markerWidth', this.getParentVWidth(), 3),
        height = this.getLength('markerHeight', this.getParentVHeight(), 3),
        viewBox = this.getViewbox('viewBox', [0, 0, width, height]);

    this.getVWidth = function () {
      return viewBox[2];
    };

    this.getVHeight = function () {
      return viewBox[3];
    };

    this.drawMarker = function (isClip, isMask, posArray, strokeWidth) {
      doc.save();
      var orient = this.attr('orient'),
          units = this.attr('markerUnits'),
          rotate = orient === 'auto' ? posArray[2] : (parseFloat(orient) || 0) * Math.PI / 180,
          scale = units === 'userSpaceOnUse' ? 1 : strokeWidth;
      doc.transform(Math.cos(rotate) * scale, Math.sin(rotate) * scale, -Math.sin(rotate) * scale, Math.cos(rotate) * scale, posArray[0], posArray[1]);
      var refX = this.getLength('refX', this.getVWidth(), 0),
          refY = this.getLength('refY', this.getVHeight(), 0),
          aspectRatioMatrix = parseAspectRatio(this.attr('preserveAspectRatio'), width, height, viewBox[2], viewBox[3], 0.5);

      if (this.get('overflow') === 'hidden') {
        doc.rect(aspectRatioMatrix[0] * (viewBox[0] + viewBox[2] / 2 - refX) - width / 2, aspectRatioMatrix[3] * (viewBox[1] + viewBox[3] / 2 - refY) - height / 2, width, height).clip();
      }

      doc.transform.apply(doc, aspectRatioMatrix);
      doc.translate(-refX, -refY);
      var group;

      if (this.get('opacity') < 1 && !isClip) {
        group = docBeginGroup(getPageBBox());
      }

      this.drawChildren(isClip, isMask);

      if (group) {
        docEndGroup(group);
        doc.fillOpacity(this.get('opacity'));
        docInsertGroup(group);
      }

      doc.restore();
    };
  };

  var SvgElemClipPath = function SvgElemClipPath(obj, inherits) {
    SvgElemHasChildren.call(this, obj, inherits);

    this.useMask = function (bBox) {
      var group = docBeginGroup(getPageBBox());
      doc.save();

      if (this.attr('clipPathUnits') === 'objectBoundingBox') {
        doc.transform(bBox[2] - bBox[0], 0, 0, bBox[3] - bBox[1], bBox[0], bBox[1]);
      }

      this.clip();
      this.drawChildren(true, false);
      doc.restore();
      docEndGroup(group);
      docApplyMask(group, true);
    };
  };

  var SvgElemMask = function SvgElemMask(obj, inherits) {
    SvgElemHasChildren.call(this, obj, inherits);

    this.useMask = function (bBox) {
      var group = docBeginGroup(getPageBBox());
      doc.save();
      var x, y, w, h;

      if (this.attr('maskUnits') === 'userSpaceOnUse') {
        x = this.getLength('x', this.getVWidth(), -0.1 * (bBox[2] - bBox[0]) + bBox[0]);
        y = this.getLength('y', this.getVHeight(), -0.1 * (bBox[3] - bBox[1]) + bBox[1]);
        w = this.getLength('width', this.getVWidth(), 1.2 * (bBox[2] - bBox[0]));
        h = this.getLength('height', this.getVHeight(), 1.2 * (bBox[3] - bBox[1]));
      } else {
        x = this.getLength('x', this.getVWidth(), -0.1) * (bBox[2] - bBox[0]) + bBox[0];
        y = this.getLength('y', this.getVHeight(), -0.1) * (bBox[3] - bBox[1]) + bBox[1];
        w = this.getLength('width', this.getVWidth(), 1.2) * (bBox[2] - bBox[0]);
        h = this.getLength('height', this.getVHeight(), 1.2) * (bBox[3] - bBox[1]);
      }

      doc.rect(x, y, w, h).clip();

      if (this.attr('maskContentUnits') === 'objectBoundingBox') {
        doc.transform(bBox[2] - bBox[0], 0, 0, bBox[3] - bBox[1], bBox[0], bBox[1]);
      }

      this.clip();
      this.drawChildren(false, true);
      doc.restore();
      docEndGroup(group);
      docApplyMask(group, true);
    };
  };

  var SvgElemTextContainer = function SvgElemTextContainer(obj, inherits) {
    SvgElemStylable.call(this, obj, inherits);
    this.allowedChildren = ['tspan', '#text', '#cdata-section', 'a'];
    this.isText = true;

    this.getBoundingShape = function () {
      var shape = new SvgShape();

      for (var i = 0; i < this._pos.length; i++) {
        var pos = this._pos[i];

        if (!pos.hidden) {
          var dx0 = pos.ascent * Math.sin(pos.rotate),
              dy0 = -pos.ascent * Math.cos(pos.rotate),
              dx1 = pos.descent * Math.sin(pos.rotate),
              dy1 = -pos.descent * Math.cos(pos.rotate),
              dx2 = pos.width * Math.cos(pos.rotate),
              dy2 = pos.width * Math.sin(pos.rotate);
          shape.M(pos.x + dx0, pos.y + dy0).L(pos.x + dx0 + dx2, pos.y + dy0 + dy2).M(pos.x + dx1 + dx2, pos.y + dy1 + dy2).L(pos.x + dx1, pos.y + dy1);
        }
      }

      return shape;
    };

    this.drawTextInDocument = function (isClip, isMask) {
      if (this.link && !isClip && !isMask) {
        this.addLink();
      }

      if (this.get('text-decoration') === 'underline') {
        this.decorate(0.05 * this._font.size, -0.075 * this._font.size, isClip, isMask);
      }

      if (this.get('text-decoration') === 'overline') {
        this.decorate(0.05 * this._font.size, getAscent(this._font.font, this._font.size) + 0.075 * this._font.size, isClip, isMask);
      }

      var fill = this.getFill(isClip, isMask),
          stroke = this.getStroke(isClip, isMask),
          strokeWidth = this.get('stroke-width');

      if (this._font.fauxBold) {
        if (!stroke) {
          stroke = fill;
          strokeWidth = this._font.size * 0.03;
        } else {
          strokeWidth += this._font.size * 0.03;
        }
      }

      var children = this.getChildren();

      for (var i = 0; i < children.length; i++) {
        var childElem = children[i];

        switch (childElem.name) {
          case 'tspan':
          case 'textPath':
          case 'a':
            if (childElem.get('display') !== 'none') {
              childElem.drawTextInDocument(isClip, isMask);
            }

            break;

          case '#text':
          case '#cdata-section':
            if (this.get('visibility') === 'hidden') {
              continue;
            }

            if (fill || stroke || isClip) {
              if (fill) {
                docFillColor(fill);
              }

              if (stroke && strokeWidth) {
                docStrokeColor(stroke);
                doc.lineWidth(strokeWidth).miterLimit(this.get('stroke-miterlimit')).lineJoin(this.get('stroke-linejoin')).lineCap(this.get('stroke-linecap')).dash(this.get('stroke-dasharray'), {
                  phase: this.get('stroke-dashoffset')
                });
              }

              docBeginText(this._font.font, this._font.size);
              docSetTextMode(!!fill, !!stroke);

              for (var j = 0, pos = childElem._pos; j < pos.length; j++) {
                if (!pos[j].hidden && isNotEqual(pos[j].width, 0)) {
                  var cos = Math.cos(pos[j].rotate),
                      sin = Math.sin(pos[j].rotate),
                      skew = this._font.fauxItalic ? -0.25 : 0;
                  docSetTextMatrix(cos * pos[j].scale, sin * pos[j].scale, cos * skew - sin, sin * skew + cos, pos[j].x, pos[j].y);
                  docWriteGlyph(pos[j].glyph);
                }
              }

              docEndText();
            }

            break;
        }
      }

      if (this.get('text-decoration') === 'line-through') {
        this.decorate(0.05 * this._font.size, 0.5 * (getAscent(this._font.font, this._font.size) + getDescent(this._font.font, this._font.size)), isClip, isMask);
      }
    };

    this.decorate = function (lineWidth, linePosition, isClip, isMask) {
      var fill = this.getFill(isClip, isMask),
          stroke = this.getStroke(isClip, isMask);

      if (fill) {
        docFillColor(fill);
      }

      if (stroke) {
        docStrokeColor(stroke);
        doc.lineWidth(this.get('stroke-width')).miterLimit(this.get('stroke-miterlimit')).lineJoin(this.get('stroke-linejoin')).lineCap(this.get('stroke-linecap')).dash(this.get('stroke-dasharray'), {
          phase: this.get('stroke-dashoffset')
        });
      }

      for (var j = 0, pos = this._pos; j < pos.length; j++) {
        if (!pos[j].hidden && isNotEqual(pos[j].width, 0)) {
          var dx0 = (linePosition + lineWidth / 2) * Math.sin(pos[j].rotate),
              dy0 = -(linePosition + lineWidth / 2) * Math.cos(pos[j].rotate),
              dx1 = (linePosition - lineWidth / 2) * Math.sin(pos[j].rotate),
              dy1 = -(linePosition - lineWidth / 2) * Math.cos(pos[j].rotate),
              dx2 = pos[j].width * Math.cos(pos[j].rotate),
              dy2 = pos[j].width * Math.sin(pos[j].rotate);
          new SvgShape().M(pos[j].x + dx0, pos[j].y + dy0).L(pos[j].x + dx0 + dx2, pos[j].y + dy0 + dy2).L(pos[j].x + dx1 + dx2, pos[j].y + dy1 + dy2).L(pos[j].x + dx1, pos[j].y + dy1).Z().insertInDocument();

          if (fill && stroke) {
            doc.fillAndStroke();
          } else if (fill) {
            doc.fill();
          } else if (stroke) {
            doc.stroke();
          }
        }
      }
    };
  };

  var SvgElemTextNode = function SvgElemTextNode(obj, inherits) {
    this.name = obj.nodeName;
    this.textContent = obj.nodeValue;
  };

  var SvgElemTspan = function SvgElemTspan(obj, inherits) {
    SvgElemTextContainer.call(this, obj, inherits);
  };

  var SvgElemTextPath = function SvgElemTextPath(obj, inherits) {
    SvgElemTextContainer.call(this, obj, inherits);
    var pathObject, pathLength, temp;

    if ((temp = this.attr('path')) && temp.trim() !== '') {
      var _pathLength = this.getLength('pathLength', this.getViewport());

      this.pathObject = new SvgShape().path(temp);
      this.pathLength = _pathLength > 0 ? _pathLength : this.pathObject.totalLength;
      this.pathScale = this.pathObject.totalLength / this.pathLength;
    } else if ((temp = this.getUrl('href') || this.getUrl('xlink:href')) && temp.nodeName === 'path') {
      var pathElem = new SvgElemPath(temp, this);
      this.pathObject = pathElem.shape.clone().transform(pathElem.get('transform'));
      this.pathLength = this.chooseValue(pathElem.pathLength, this.pathObject.totalLength);
      this.pathScale = this.pathObject.totalLength / this.pathLength;
    }
  };

  var SvgElemText = function SvgElemText(obj, inherits) {
    SvgElemTextContainer.call(this, obj, inherits);
    this.allowedChildren = ['textPath', 'tspan', '#text', '#cdata-section', 'a'];

    (function (textParentElem) {
      var processedText = '',
          remainingText = obj.textContent,
          textPaths = [],
          currentChunk = [],
          currentAnchor,
          currentDirection,
          currentX = 0,
          currentY = 0;

      function doAnchoring() {
        if (currentChunk.length) {
          var last = currentChunk[currentChunk.length - 1];
          var first = currentChunk[0];
          var width = last.x + last.width - first.x;
          var anchordx = {
            'startltr': 0,
            'middleltr': 0.5,
            'endltr': 1,
            'startrtl': 1,
            'middlertl': 0.5,
            'endrtl': 0
          }[currentAnchor + currentDirection] * width || 0;

          for (var i = 0; i < currentChunk.length; i++) {
            currentChunk[i].x -= anchordx;
          }
        }

        currentChunk = [];
      }

      function adjustLength(pos, length, spacingAndGlyphs) {
        var firstChar = pos[0],
            lastChar = pos[pos.length - 1],
            startX = firstChar.x,
            endX = lastChar.x + lastChar.width;

        if (spacingAndGlyphs) {
          var textScale = length / (endX - startX);

          if (textScale > 0 && textScale < Infinity) {
            for (var j = 0; j < pos.length; j++) {
              pos[j].x = startX + textScale * (pos[j].x - startX);
              pos[j].scale *= textScale;
              pos[j].width *= textScale;
            }
          }
        } else {
          if (pos.length >= 2) {
            var spaceDiff = (length - (endX - startX)) / (pos.length - 1);

            for (var _j4 = 0; _j4 < pos.length; _j4++) {
              pos[_j4].x += _j4 * spaceDiff;
            }
          }
        }

        currentX += length - (endX - startX);
      }

      function recursive(currentElem, parentElem) {
        currentElem._x = combineArrays(currentElem.getLengthList('x', currentElem.getVWidth()), parentElem ? parentElem._x.slice(parentElem._pos.length) : []);
        currentElem._y = combineArrays(currentElem.getLengthList('y', currentElem.getVHeight()), parentElem ? parentElem._y.slice(parentElem._pos.length) : []);
        currentElem._dx = combineArrays(currentElem.getLengthList('dx', currentElem.getVWidth()), parentElem ? parentElem._dx.slice(parentElem._pos.length) : []);
        currentElem._dy = combineArrays(currentElem.getLengthList('dy', currentElem.getVHeight()), parentElem ? parentElem._dy.slice(parentElem._pos.length) : []);
        currentElem._rot = combineArrays(currentElem.getNumberList('rotate'), parentElem ? parentElem._rot.slice(parentElem._pos.length) : []);
        currentElem._defRot = currentElem.chooseValue(currentElem._rot[currentElem._rot.length - 1], parentElem && parentElem._defRot, 0);

        if (currentElem.name === 'textPath') {
          currentElem._y = [];
        }

        var fontOptions = {
          fauxItalic: false,
          fauxBold: false
        },
            fontNameorLink = fontCallback(currentElem.get('font-family'), currentElem.get('font-weight') === 'bold', currentElem.get('font-style') === 'italic', fontOptions);

        try {
          doc.font(fontNameorLink);
        } catch (e) {
          warningCallback('SVGElemText: failed to open font "' + fontNameorLink + '" in PDFKit');
        }

        currentElem._pos = [];
        currentElem._index = 0;
        currentElem._font = {
          font: doc._font,
          size: currentElem.get('font-size'),
          fauxItalic: fontOptions.fauxItalic,
          fauxBold: fontOptions.fauxBold
        };
        var textLength = currentElem.getLength('textLength', currentElem.getVWidth(), undefined),
            spacingAndGlyphs = currentElem.attr('lengthAdjust') === 'spacingAndGlyphs',
            wordSpacing = currentElem.get('word-spacing'),
            letterSpacing = currentElem.get('letter-spacing'),
            textAnchor = currentElem.get('text-anchor'),
            textDirection = currentElem.get('direction'),
            baseline = getBaseline(currentElem._font.font, currentElem._font.size, currentElem.get('alignment-baseline') || currentElem.get('dominant-baseline'), currentElem.get('baseline-shift'));

        if (currentElem.name === 'textPath') {
          doAnchoring();
          currentX = currentY = 0;
        }

        var children = currentElem.getChildren();

        for (var i = 0; i < children.length; i++) {
          var childElem = children[i];

          switch (childElem.name) {
            case 'tspan':
            case 'textPath':
            case 'a':
              recursive(childElem, currentElem);
              break;

            case '#text':
            case '#cdata-section':
              var rawText = childElem.textContent,
                  renderedText = rawText,
                  words = void 0;
              childElem._font = currentElem._font;
              childElem._pos = [];
              remainingText = remainingText.substring(rawText.length);

              if (currentElem.get('xml:space') === 'preserve') {
                renderedText = renderedText.replace(/[\s]/g, ' ');
              } else {
                renderedText = renderedText.replace(/[\s]+/g, ' ');

                if (processedText.match(/[\s]$|^$/)) {
                  renderedText = renderedText.replace(/^[\s]/, '');
                }

                if (remainingText.match(/^[\s]*$/)) {
                  renderedText = renderedText.replace(/[\s]$/, '');
                }
              }

              processedText += rawText;

              if (wordSpacing === 0) {
                words = [renderedText];
              } else {
                words = renderedText.split(/(\s)/);
              }

              for (var w = 0; w < words.length; w++) {
                var pos = getTextPos(currentElem._font.font, currentElem._font.size, words[w]);

                for (var j = 0; j < pos.length; j++) {
                  var index = currentElem._index,
                      xAttr = currentElem._x[index],
                      yAttr = currentElem._y[index],
                      dxAttr = currentElem._dx[index],
                      dyAttr = currentElem._dy[index],
                      rotAttr = currentElem._rot[index],
                      continuous = !(w === 0 && j === 0);

                  if (xAttr !== undefined) {
                    continuous = false;
                    doAnchoring();
                    currentX = xAttr;
                  }

                  if (yAttr !== undefined) {
                    continuous = false;
                    doAnchoring();
                    currentY = yAttr;
                  }

                  if (dxAttr !== undefined) {
                    continuous = false;
                    currentX += dxAttr;
                  }

                  if (dyAttr !== undefined) {
                    continuous = false;
                    currentY += dyAttr;
                  }

                  if (rotAttr !== undefined || currentElem._defRot !== 0) {
                    continuous = false;
                  }

                  var position = {
                    glyph: pos[j].glyph,
                    rotate: Math.PI / 180 * currentElem.chooseValue(rotAttr, currentElem._defRot),
                    x: currentX + pos[j].xOffset,
                    y: currentY + baseline + pos[j].yOffset,
                    width: pos[j].width,
                    ascent: getAscent(currentElem._font.font, currentElem._font.size),
                    descent: getDescent(currentElem._font.font, currentElem._font.size),
                    scale: 1,
                    hidden: false,
                    continuous: continuous
                  };
                  currentChunk.push(position);

                  childElem._pos.push(position);

                  currentElem._pos.push(position);

                  currentElem._index += pos[j].unicode.length;

                  if (currentChunk.length === 1) {
                    currentAnchor = textAnchor;
                    currentDirection = textDirection;
                  }

                  currentX += pos[j].xAdvance + letterSpacing;
                  currentY += pos[j].yAdvance;
                }

                if (words[w] === ' ') {
                  currentX += wordSpacing;
                }
              }

              break;

            default:
              remainingText = remainingText.substring(childElem.textContent.length);
          }
        }

        if (textLength && currentElem._pos.length) {
          adjustLength(currentElem._pos, textLength, spacingAndGlyphs);
        }

        if (currentElem.name === 'textPath' || currentElem.name === 'text') {
          doAnchoring();
        }

        if (currentElem.name === 'textPath') {
          textPaths.push(currentElem);
          var pathObject = currentElem.pathObject;

          if (pathObject) {
            currentX = pathObject.endPoint[0];
            currentY = pathObject.endPoint[1];
          }
        }

        if (parentElem) {
          parentElem._pos = parentElem._pos.concat(currentElem._pos);
          parentElem._index += currentElem._index;
        }
      }

      function textOnPath(currentElem) {
        var pathObject = currentElem.pathObject,
            pathLength = currentElem.pathLength,
            pathScale = currentElem.pathScale;

        if (pathObject) {
          var textOffset = currentElem.getLength('startOffset', pathLength, 0);

          for (var j = 0; j < currentElem._pos.length; j++) {
            var charMidX = textOffset + currentElem._pos[j].x + 0.5 * currentElem._pos[j].width;

            if (charMidX > pathLength || charMidX < 0) {
              currentElem._pos[j].hidden = true;
            } else {
              var pointOnPath = pathObject.getPointAtLength(charMidX * pathScale);

              if (isNotEqual(pathScale, 1)) {
                currentElem._pos[j].scale *= pathScale;
                currentElem._pos[j].width *= pathScale;
              }

              currentElem._pos[j].x = pointOnPath[0] - 0.5 * currentElem._pos[j].width * Math.cos(pointOnPath[2]) - currentElem._pos[j].y * Math.sin(pointOnPath[2]);
              currentElem._pos[j].y = pointOnPath[1] - 0.5 * currentElem._pos[j].width * Math.sin(pointOnPath[2]) + currentElem._pos[j].y * Math.cos(pointOnPath[2]);
              currentElem._pos[j].rotate = pointOnPath[2] + currentElem._pos[j].rotate;
              currentElem._pos[j].continuous = false;
            }
          }
        } else {
          for (var _j5 = 0; _j5 < currentElem._pos.length; _j5++) {
            currentElem._pos[_j5].hidden = true;
          }
        }
      }

      recursive(textParentElem, null);

      for (var i = 0; i < textPaths.length; i++) {
        textOnPath(textPaths[i]);
      }
    })(this);

    this.getTransformation = function () {
      return this.get('transform');
    };

    this.drawInDocument = function (isClip, isMask) {
      doc.save();
      this.transform();
      this.clip();
      var masked = this.mask(),
          group;

      if (masked) {
        group = docBeginGroup(getPageBBox());
      }

      this.drawTextInDocument(isClip, isMask);

      if (group) {
        docEndGroup(group);
        docInsertGroup(group);
      }

      doc.restore();
    };
  };

  options = options || {};
  var pxToPt = options.assumePt ? 1 : 72 / 96,
      // 1px = 72/96pt, but only if assumePt is false
  viewportWidth = (options.width || doc.page.width) / pxToPt,
      viewportHeight = (options.height || doc.page.height) / pxToPt,
      preserveAspectRatio = options.preserveAspectRatio || null,
      // default to null so that the attr can override if not passed
  useCSS = options.useCSS && typeof SVGElement !== 'undefined' && svg instanceof SVGElement && typeof getComputedStyle === 'function',
      warningCallback = options.warningCallback,
      fontCallback = options.fontCallback,
      imageCallback = options.imageCallback,
      colorCallback = options.colorCallback,
      documentCallback = options.documentCallback,
      precision = Math.ceil(Math.max(1, options.precision)) || 3,
      groupStack = [],
      documentCache = {},
      links = [],
      styleRules = [];

  if (typeof warningCallback !== 'function') {
    warningCallback = function warningCallback(str) {
      if (typeof console !== undefined && typeof console.warn === 'function') {
        console.warn(str);
      }
    };
  }

  if (typeof fontCallback !== 'function') {
    fontCallback = function fontCallback(family, bold, italic, fontOptions) {
      // Check if the font is already registered in the document
      if (bold && italic) {
        if (doc._registeredFonts.hasOwnProperty(family + '-BoldItalic')) {
          return family + '-BoldItalic';
        } else if (doc._registeredFonts.hasOwnProperty(family + '-Italic')) {
          fontOptions.fauxBold = true;
          return family + '-Italic';
        } else if (doc._registeredFonts.hasOwnProperty(family + '-Bold')) {
          fontOptions.fauxItalic = true;
          return family + '-Bold';
        } else if (doc._registeredFonts.hasOwnProperty(family)) {
          fontOptions.fauxBold = true;
          fontOptions.fauxItalic = true;
          return family;
        }
      }

      if (bold && !italic) {
        if (doc._registeredFonts.hasOwnProperty(family + '-Bold')) {
          return family + '-Bold';
        } else if (doc._registeredFonts.hasOwnProperty(family)) {
          fontOptions.fauxBold = true;
          return family;
        }
      }

      if (!bold && italic) {
        if (doc._registeredFonts.hasOwnProperty(family + '-Italic')) {
          return family + '-Italic';
        } else if (doc._registeredFonts.hasOwnProperty(family)) {
          fontOptions.fauxItalic = true;
          return family;
        }
      }

      if (!bold && !italic) {
        if (doc._registeredFonts.hasOwnProperty(family)) {
          return family;
        }
      } // Use standard fonts as fallback


      if (family.match(/(?:^|,)\s*serif\s*$/)) {
        if (bold && italic) {
          return 'Times-BoldItalic';
        }

        if (bold && !italic) {
          return 'Times-Bold';
        }

        if (!bold && italic) {
          return 'Times-Italic';
        }

        if (!bold && !italic) {
          return 'Times-Roman';
        }
      } else if (family.match(/(?:^|,)\s*monospace\s*$/)) {
        if (bold && italic) {
          return 'Courier-BoldOblique';
        }

        if (bold && !italic) {
          return 'Courier-Bold';
        }

        if (!bold && italic) {
          return 'Courier-Oblique';
        }

        if (!bold && !italic) {
          return 'Courier';
        }
      } else if (family.match(/(?:^|,)\s*sans-serif\s*$/) || true) {
        if (bold && italic) {
          return 'Helvetica-BoldOblique';
        }

        if (bold && !italic) {
          return 'Helvetica-Bold';
        }

        if (!bold && italic) {
          return 'Helvetica-Oblique';
        }

        if (!bold && !italic) {
          return 'Helvetica';
        }
      }
    };
  }

  if (typeof imageCallback !== 'function') {
    imageCallback = function imageCallback(link) {
      return link.replace(/\s+/g, '');
    };
  }

  if (typeof colorCallback !== 'function') {
    colorCallback = null;
  } else {
    for (var color in DefaultColors) {
      var newColor = colorCallback(DefaultColors[color]);
      DefaultColors[color][0] = newColor[0];
      DefaultColors[color][1] = newColor[1];
    }
  }

  if (typeof documentCallback !== 'function') {
    documentCallback = null;
  }

  if (typeof svg === 'string') {
    svg = parseXml(svg);
  }

  if (svg) {
    var styles = svg.getElementsByTagName('style');

    for (var i = 0; i < styles.length; i++) {
      styleRules = styleRules.concat(parseStyleSheet(styles[i].textContent));
    }

    var elem = createSVGElement(svg, null);

    if (typeof elem.drawInDocument === 'function') {
      if (options.useCSS && !useCSS) {
        warningCallback('SVGtoPDF: useCSS option can only be used for SVG *elements* in compatible browsers');
      }

      var savedFillColor = doc._fillColor;
      doc.save().translate(x || 0, y || 0).scale(pxToPt);
      elem.drawInDocument();

      for (var _i8 = 0; _i8 < links.length; _i8++) {
        doc.page.annotations.push(links[_i8]);
      }

      doc.restore();
      doc._fillColor = savedFillColor;
    } else {
      warningCallback('SVGtoPDF: this element can\'t be rendered directly: ' + svg.nodeName);
    }
  } else {
    warningCallback('SVGtoPDF: the input does not look like a valid SVG');
  }
};

if ( true && module && typeof module.exports !== 'undefined') {
  module.exports = SVGtoPDF;
}
/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(548)(module)))

/***/ }),
/* 548 */
/***/ (function(module, exports) {

module.exports = function(module) {
	if (!module.webpackPolyfill) {
		module.deprecate = function() {};
		module.paths = [];
		// module.parent = undefined by default
		if (!module.children) module.children = [];
		Object.defineProperty(module, "loaded", {
			enumerable: true,
			get: function() {
				return module.l;
			}
		});
		Object.defineProperty(module, "id", {
			enumerable: true,
			get: function() {
				return module.i;
			}
		});
		module.webpackPolyfill = 1;
	}
	return module;
};


/***/ }),
/* 549 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";


// Internet Explorer polyfills
if (typeof window !== 'undefined' && !window.Promise) {
	__webpack_require__(550);
}
__webpack_require__(560);

var fetchUrl = function (url) {
	return new Promise(function (resolve, reject) {
		var xhr = new XMLHttpRequest();
		xhr.open('GET', url, true);
		xhr.responseType = 'arraybuffer';

		xhr.onreadystatechange = function () {
			if (xhr.readyState !== 4) {
				return;
			}

			var ok = xhr.status >= 200 && xhr.status < 300;
			if (!ok) {
				setTimeout(function () {
					reject(new TypeError('Failed to fetch (url: "' + url + '")'));
				}, 0);
			}
		};

		xhr.onload = function () {
			var ok = xhr.status >= 200 && xhr.status < 300;
			if (ok) {
				resolve(xhr.response);
			}
		};

		xhr.onerror = function () {
			setTimeout(function () {
				reject(new TypeError('Network request failed (url: "' + url + '")'));
			}, 0);
		};

		xhr.ontimeout = function () {
			setTimeout(function () {
				reject(new TypeError('Network request failed (url: "' + url + '")'));
			}, 0);
		};

		xhr.send();
	});
};

function URLBrowserResolver(fs) {
	this.fs = fs;
	this.resolving = {};
}

URLBrowserResolver.prototype.resolve = function (url) {
	if (!this.resolving[url]) {
		var _this = this;
		this.resolving[url] = new Promise(function (resolve, reject) {
			if (url.toLowerCase().indexOf('https://') === 0 || url.toLowerCase().indexOf('http://') === 0) {
				fetchUrl(url).then(function (buffer) {
					_this.fs.writeFileSync(url, buffer);
					resolve();
				}, function (result) {
					reject(result);
				});
			} else {
				// cannot be resolved
				resolve();
			}
		});
	}

	return this.resolving[url];
}

URLBrowserResolver.prototype.resolved = function () {
	var _this = this;
	return new Promise(function (resolve, reject) {
		Promise.all(Object.values(_this.resolving)).then(function () {
			resolve();
		}, function (result) {
			reject(result);
		});
	});
}

module.exports = URLBrowserResolver;


/***/ }),
/* 550 */
/***/ (function(module, exports, __webpack_require__) {

var parent = __webpack_require__(551);
__webpack_require__(556);
// TODO: Remove from `core-js@4`
__webpack_require__(557);
__webpack_require__(558);
__webpack_require__(559);

module.exports = parent;


/***/ }),
/* 551 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(32);
__webpack_require__(159);
__webpack_require__(120);
__webpack_require__(552);
__webpack_require__(299);
__webpack_require__(555);
var path = __webpack_require__(114);

module.exports = path.Promise;


/***/ }),
/* 552 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var IS_PURE = __webpack_require__(56);
var global = __webpack_require__(3);
var getBuiltIn = __webpack_require__(36);
var NativePromise = __webpack_require__(295);
var redefine = __webpack_require__(25);
var redefineAll = __webpack_require__(221);
var setToStringTag = __webpack_require__(95);
var setSpecies = __webpack_require__(156);
var isObject = __webpack_require__(13);
var aFunction = __webpack_require__(44);
var anInstance = __webpack_require__(155);
var classof = __webpack_require__(30);
var inspectSource = __webpack_require__(140);
var iterate = __webpack_require__(136);
var checkCorrectnessOfIteration = __webpack_require__(153);
var speciesConstructor = __webpack_require__(38);
var task = __webpack_require__(296).set;
var microtask = __webpack_require__(553);
var promiseResolve = __webpack_require__(298);
var hostReportErrors = __webpack_require__(554);
var newPromiseCapabilityModule = __webpack_require__(109);
var perform = __webpack_require__(137);
var InternalStateModule = __webpack_require__(35);
var isForced = __webpack_require__(144);
var wellKnownSymbol = __webpack_require__(6);
var V8_VERSION = __webpack_require__(147);

var SPECIES = wellKnownSymbol('species');
var PROMISE = 'Promise';
var getInternalState = InternalStateModule.get;
var setInternalState = InternalStateModule.set;
var getInternalPromiseState = InternalStateModule.getterFor(PROMISE);
var PromiseConstructor = NativePromise;
var TypeError = global.TypeError;
var document = global.document;
var process = global.process;
var $fetch = getBuiltIn('fetch');
var newPromiseCapability = newPromiseCapabilityModule.f;
var newGenericPromiseCapability = newPromiseCapability;
var IS_NODE = classof(process) == 'process';
var DISPATCH_EVENT = !!(document && document.createEvent && global.dispatchEvent);
var UNHANDLED_REJECTION = 'unhandledrejection';
var REJECTION_HANDLED = 'rejectionhandled';
var PENDING = 0;
var FULFILLED = 1;
var REJECTED = 2;
var HANDLED = 1;
var UNHANDLED = 2;
var Internal, OwnPromiseCapability, PromiseWrapper, nativeThen;

var FORCED = isForced(PROMISE, function () {
  var GLOBAL_CORE_JS_PROMISE = inspectSource(PromiseConstructor) !== String(PromiseConstructor);
  if (!GLOBAL_CORE_JS_PROMISE) {
    // V8 6.6 (Node 10 and Chrome 66) have a bug with resolving custom thenables
    // https://bugs.chromium.org/p/chromium/issues/detail?id=830565
    // We can't detect it synchronously, so just check versions
    if (V8_VERSION === 66) return true;
    // Unhandled rejections tracking support, NodeJS Promise without it fails @@species test
    if (!IS_NODE && typeof PromiseRejectionEvent != 'function') return true;
  }
  // We need Promise#finally in the pure version for preventing prototype pollution
  if (IS_PURE && !PromiseConstructor.prototype['finally']) return true;
  // We can't use @@species feature detection in V8 since it causes
  // deoptimization and performance degradation
  // https://github.com/zloirock/core-js/issues/679
  if (V8_VERSION >= 51 && /native code/.test(PromiseConstructor)) return false;
  // Detect correctness of subclassing with @@species support
  var promise = PromiseConstructor.resolve(1);
  var FakePromise = function (exec) {
    exec(function () { /* empty */ }, function () { /* empty */ });
  };
  var constructor = promise.constructor = {};
  constructor[SPECIES] = FakePromise;
  return !(promise.then(function () { /* empty */ }) instanceof FakePromise);
});

var INCORRECT_ITERATION = FORCED || !checkCorrectnessOfIteration(function (iterable) {
  PromiseConstructor.all(iterable)['catch'](function () { /* empty */ });
});

// helpers
var isThenable = function (it) {
  var then;
  return isObject(it) && typeof (then = it.then) == 'function' ? then : false;
};

var notify = function (promise, state, isReject) {
  if (state.notified) return;
  state.notified = true;
  var chain = state.reactions;
  microtask(function () {
    var value = state.value;
    var ok = state.state == FULFILLED;
    var index = 0;
    // variable length - can't use forEach
    while (chain.length > index) {
      var reaction = chain[index++];
      var handler = ok ? reaction.ok : reaction.fail;
      var resolve = reaction.resolve;
      var reject = reaction.reject;
      var domain = reaction.domain;
      var result, then, exited;
      try {
        if (handler) {
          if (!ok) {
            if (state.rejection === UNHANDLED) onHandleUnhandled(promise, state);
            state.rejection = HANDLED;
          }
          if (handler === true) result = value;
          else {
            if (domain) domain.enter();
            result = handler(value); // can throw
            if (domain) {
              domain.exit();
              exited = true;
            }
          }
          if (result === reaction.promise) {
            reject(TypeError('Promise-chain cycle'));
          } else if (then = isThenable(result)) {
            then.call(result, resolve, reject);
          } else resolve(result);
        } else reject(value);
      } catch (error) {
        if (domain && !exited) domain.exit();
        reject(error);
      }
    }
    state.reactions = [];
    state.notified = false;
    if (isReject && !state.rejection) onUnhandled(promise, state);
  });
};

var dispatchEvent = function (name, promise, reason) {
  var event, handler;
  if (DISPATCH_EVENT) {
    event = document.createEvent('Event');
    event.promise = promise;
    event.reason = reason;
    event.initEvent(name, false, true);
    global.dispatchEvent(event);
  } else event = { promise: promise, reason: reason };
  if (handler = global['on' + name]) handler(event);
  else if (name === UNHANDLED_REJECTION) hostReportErrors('Unhandled promise rejection', reason);
};

var onUnhandled = function (promise, state) {
  task.call(global, function () {
    var value = state.value;
    var IS_UNHANDLED = isUnhandled(state);
    var result;
    if (IS_UNHANDLED) {
      result = perform(function () {
        if (IS_NODE) {
          process.emit('unhandledRejection', value, promise);
        } else dispatchEvent(UNHANDLED_REJECTION, promise, value);
      });
      // Browsers should not trigger `rejectionHandled` event if it was handled here, NodeJS - should
      state.rejection = IS_NODE || isUnhandled(state) ? UNHANDLED : HANDLED;
      if (result.error) throw result.value;
    }
  });
};

var isUnhandled = function (state) {
  return state.rejection !== HANDLED && !state.parent;
};

var onHandleUnhandled = function (promise, state) {
  task.call(global, function () {
    if (IS_NODE) {
      process.emit('rejectionHandled', promise);
    } else dispatchEvent(REJECTION_HANDLED, promise, state.value);
  });
};

var bind = function (fn, promise, state, unwrap) {
  return function (value) {
    fn(promise, state, value, unwrap);
  };
};

var internalReject = function (promise, state, value, unwrap) {
  if (state.done) return;
  state.done = true;
  if (unwrap) state = unwrap;
  state.value = value;
  state.state = REJECTED;
  notify(promise, state, true);
};

var internalResolve = function (promise, state, value, unwrap) {
  if (state.done) return;
  state.done = true;
  if (unwrap) state = unwrap;
  try {
    if (promise === value) throw TypeError("Promise can't be resolved itself");
    var then = isThenable(value);
    if (then) {
      microtask(function () {
        var wrapper = { done: false };
        try {
          then.call(value,
            bind(internalResolve, promise, wrapper, state),
            bind(internalReject, promise, wrapper, state)
          );
        } catch (error) {
          internalReject(promise, wrapper, error, state);
        }
      });
    } else {
      state.value = value;
      state.state = FULFILLED;
      notify(promise, state, false);
    }
  } catch (error) {
    internalReject(promise, { done: false }, error, state);
  }
};

// constructor polyfill
if (FORCED) {
  // 25.4.3.1 Promise(executor)
  PromiseConstructor = function Promise(executor) {
    anInstance(this, PromiseConstructor, PROMISE);
    aFunction(executor);
    Internal.call(this);
    var state = getInternalState(this);
    try {
      executor(bind(internalResolve, this, state), bind(internalReject, this, state));
    } catch (error) {
      internalReject(this, state, error);
    }
  };
  // eslint-disable-next-line no-unused-vars
  Internal = function Promise(executor) {
    setInternalState(this, {
      type: PROMISE,
      done: false,
      notified: false,
      parent: false,
      reactions: [],
      rejection: false,
      state: PENDING,
      value: undefined
    });
  };
  Internal.prototype = redefineAll(PromiseConstructor.prototype, {
    // `Promise.prototype.then` method
    // https://tc39.github.io/ecma262/#sec-promise.prototype.then
    then: function then(onFulfilled, onRejected) {
      var state = getInternalPromiseState(this);
      var reaction = newPromiseCapability(speciesConstructor(this, PromiseConstructor));
      reaction.ok = typeof onFulfilled == 'function' ? onFulfilled : true;
      reaction.fail = typeof onRejected == 'function' && onRejected;
      reaction.domain = IS_NODE ? process.domain : undefined;
      state.parent = true;
      state.reactions.push(reaction);
      if (state.state != PENDING) notify(this, state, false);
      return reaction.promise;
    },
    // `Promise.prototype.catch` method
    // https://tc39.github.io/ecma262/#sec-promise.prototype.catch
    'catch': function (onRejected) {
      return this.then(undefined, onRejected);
    }
  });
  OwnPromiseCapability = function () {
    var promise = new Internal();
    var state = getInternalState(promise);
    this.promise = promise;
    this.resolve = bind(internalResolve, promise, state);
    this.reject = bind(internalReject, promise, state);
  };
  newPromiseCapabilityModule.f = newPromiseCapability = function (C) {
    return C === PromiseConstructor || C === PromiseWrapper
      ? new OwnPromiseCapability(C)
      : newGenericPromiseCapability(C);
  };

  if (!IS_PURE && typeof NativePromise == 'function') {
    nativeThen = NativePromise.prototype.then;

    // wrap native Promise#then for native async functions
    redefine(NativePromise.prototype, 'then', function then(onFulfilled, onRejected) {
      var that = this;
      return new PromiseConstructor(function (resolve, reject) {
        nativeThen.call(that, resolve, reject);
      }).then(onFulfilled, onRejected);
    // https://github.com/zloirock/core-js/issues/640
    }, { unsafe: true });

    // wrap fetch result
    if (typeof $fetch == 'function') $({ global: true, enumerable: true, forced: true }, {
      // eslint-disable-next-line no-unused-vars
      fetch: function fetch(input /* , init */) {
        return promiseResolve(PromiseConstructor, $fetch.apply(global, arguments));
      }
    });
  }
}

$({ global: true, wrap: true, forced: FORCED }, {
  Promise: PromiseConstructor
});

setToStringTag(PromiseConstructor, PROMISE, false, true);
setSpecies(PROMISE);

PromiseWrapper = getBuiltIn(PROMISE);

// statics
$({ target: PROMISE, stat: true, forced: FORCED }, {
  // `Promise.reject` method
  // https://tc39.github.io/ecma262/#sec-promise.reject
  reject: function reject(r) {
    var capability = newPromiseCapability(this);
    capability.reject.call(undefined, r);
    return capability.promise;
  }
});

$({ target: PROMISE, stat: true, forced: IS_PURE || FORCED }, {
  // `Promise.resolve` method
  // https://tc39.github.io/ecma262/#sec-promise.resolve
  resolve: function resolve(x) {
    return promiseResolve(IS_PURE && this === PromiseWrapper ? PromiseConstructor : this, x);
  }
});

$({ target: PROMISE, stat: true, forced: INCORRECT_ITERATION }, {
  // `Promise.all` method
  // https://tc39.github.io/ecma262/#sec-promise.all
  all: function all(iterable) {
    var C = this;
    var capability = newPromiseCapability(C);
    var resolve = capability.resolve;
    var reject = capability.reject;
    var result = perform(function () {
      var $promiseResolve = aFunction(C.resolve);
      var values = [];
      var counter = 0;
      var remaining = 1;
      iterate(iterable, function (promise) {
        var index = counter++;
        var alreadyCalled = false;
        values.push(undefined);
        remaining++;
        $promiseResolve.call(C, promise).then(function (value) {
          if (alreadyCalled) return;
          alreadyCalled = true;
          values[index] = value;
          --remaining || resolve(values);
        }, reject);
      });
      --remaining || resolve(values);
    });
    if (result.error) reject(result.value);
    return capability.promise;
  },
  // `Promise.race` method
  // https://tc39.github.io/ecma262/#sec-promise.race
  race: function race(iterable) {
    var C = this;
    var capability = newPromiseCapability(C);
    var reject = capability.reject;
    var result = perform(function () {
      var $promiseResolve = aFunction(C.resolve);
      iterate(iterable, function (promise) {
        $promiseResolve.call(C, promise).then(capability.resolve, reject);
      });
    });
    if (result.error) reject(result.value);
    return capability.promise;
  }
});


/***/ }),
/* 553 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);
var getOwnPropertyDescriptor = __webpack_require__(54).f;
var classof = __webpack_require__(30);
var macrotask = __webpack_require__(296).set;
var IS_IOS = __webpack_require__(297);

var MutationObserver = global.MutationObserver || global.WebKitMutationObserver;
var process = global.process;
var Promise = global.Promise;
var IS_NODE = classof(process) == 'process';
// Node.js 11 shows ExperimentalWarning on getting `queueMicrotask`
var queueMicrotaskDescriptor = getOwnPropertyDescriptor(global, 'queueMicrotask');
var queueMicrotask = queueMicrotaskDescriptor && queueMicrotaskDescriptor.value;

var flush, head, last, notify, toggle, node, promise, then;

// modern engines have queueMicrotask method
if (!queueMicrotask) {
  flush = function () {
    var parent, fn;
    if (IS_NODE && (parent = process.domain)) parent.exit();
    while (head) {
      fn = head.fn;
      head = head.next;
      try {
        fn();
      } catch (error) {
        if (head) notify();
        else last = undefined;
        throw error;
      }
    } last = undefined;
    if (parent) parent.enter();
  };

  // Node.js
  if (IS_NODE) {
    notify = function () {
      process.nextTick(flush);
    };
  // browsers with MutationObserver, except iOS - https://github.com/zloirock/core-js/issues/339
  } else if (MutationObserver && !IS_IOS) {
    toggle = true;
    node = document.createTextNode('');
    new MutationObserver(flush).observe(node, { characterData: true });
    notify = function () {
      node.data = toggle = !toggle;
    };
  // environments with maybe non-completely correct, but existent Promise
  } else if (Promise && Promise.resolve) {
    // Promise.resolve without an argument throws an error in LG WebOS 2
    promise = Promise.resolve(undefined);
    then = promise.then;
    notify = function () {
      then.call(promise, flush);
    };
  // for other environments - macrotask based on:
  // - setImmediate
  // - MessageChannel
  // - window.postMessag
  // - onreadystatechange
  // - setTimeout
  } else {
    notify = function () {
      // strange IE + webpack dev server bug - use .call(global)
      macrotask.call(global, flush);
    };
  }
}

module.exports = queueMicrotask || function (fn) {
  var task = { fn: fn, next: undefined };
  if (last) last.next = task;
  if (!head) {
    head = task;
    notify();
  } last = task;
};


/***/ }),
/* 554 */
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__(3);

module.exports = function (a, b) {
  var console = global.console;
  if (console && console.error) {
    arguments.length === 1 ? console.error(a) : console.error(a, b);
  }
};


/***/ }),
/* 555 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var IS_PURE = __webpack_require__(56);
var NativePromise = __webpack_require__(295);
var fails = __webpack_require__(4);
var getBuiltIn = __webpack_require__(36);
var speciesConstructor = __webpack_require__(38);
var promiseResolve = __webpack_require__(298);
var redefine = __webpack_require__(25);

// Safari bug https://bugs.webkit.org/show_bug.cgi?id=200829
var NON_GENERIC = !!NativePromise && fails(function () {
  NativePromise.prototype['finally'].call({ then: function () { /* empty */ } }, function () { /* empty */ });
});

// `Promise.prototype.finally` method
// https://tc39.github.io/ecma262/#sec-promise.prototype.finally
$({ target: 'Promise', proto: true, real: true, forced: NON_GENERIC }, {
  'finally': function (onFinally) {
    var C = speciesConstructor(this, getBuiltIn('Promise'));
    var isFunction = typeof onFinally == 'function';
    return this.then(
      isFunction ? function (x) {
        return promiseResolve(C, onFinally()).then(function () { return x; });
      } : onFinally,
      isFunction ? function (e) {
        return promiseResolve(C, onFinally()).then(function () { throw e; });
      } : onFinally
    );
  }
});

// patch native Promise.prototype for native async functions
if (!IS_PURE && typeof NativePromise == 'function' && !NativePromise.prototype['finally']) {
  redefine(NativePromise.prototype, 'finally', getBuiltIn('Promise').prototype['finally']);
}


/***/ }),
/* 556 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var DESCRIPTORS = __webpack_require__(11);
var getPrototypeOf = __webpack_require__(60);
var setPrototypeOf = __webpack_require__(45);
var create = __webpack_require__(58);
var defineProperty = __webpack_require__(15);
var createPropertyDescriptor = __webpack_require__(42);
var iterate = __webpack_require__(136);
var createNonEnumerableProperty = __webpack_require__(18);
var InternalStateModule = __webpack_require__(35);

var setInternalState = InternalStateModule.set;
var getInternalAggregateErrorState = InternalStateModule.getterFor('AggregateError');

var $AggregateError = function AggregateError(errors, message) {
  var that = this;
  if (!(that instanceof $AggregateError)) return new $AggregateError(errors, message);
  if (setPrototypeOf) {
    that = setPrototypeOf(new Error(message), getPrototypeOf(that));
  }
  var errorsArray = [];
  iterate(errors, errorsArray.push, errorsArray);
  if (DESCRIPTORS) setInternalState(that, { errors: errorsArray, type: 'AggregateError' });
  else that.errors = errorsArray;
  if (message !== undefined) createNonEnumerableProperty(that, 'message', String(message));
  return that;
};

$AggregateError.prototype = create(Error.prototype, {
  constructor: createPropertyDescriptor(5, $AggregateError),
  message: createPropertyDescriptor(5, ''),
  name: createPropertyDescriptor(5, 'AggregateError')
});

if (DESCRIPTORS) defineProperty.f($AggregateError.prototype, 'errors', {
  get: function () {
    return getInternalAggregateErrorState(this).errors;
  },
  configurable: true
});

$({ global: true }, {
  AggregateError: $AggregateError
});


/***/ }),
/* 557 */
/***/ (function(module, exports, __webpack_require__) {

// TODO: Remove from `core-js@4`
__webpack_require__(299);


/***/ }),
/* 558 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var newPromiseCapabilityModule = __webpack_require__(109);
var perform = __webpack_require__(137);

// `Promise.try` method
// https://github.com/tc39/proposal-promise-try
$({ target: 'Promise', stat: true }, {
  'try': function (callbackfn) {
    var promiseCapability = newPromiseCapabilityModule.f(this);
    var result = perform(callbackfn);
    (result.error ? promiseCapability.reject : promiseCapability.resolve)(result.value);
    return promiseCapability.promise;
  }
});


/***/ }),
/* 559 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(1);
var aFunction = __webpack_require__(44);
var getBuiltIn = __webpack_require__(36);
var newPromiseCapabilityModule = __webpack_require__(109);
var perform = __webpack_require__(137);
var iterate = __webpack_require__(136);

var PROMISE_ANY_ERROR = 'No one promise resolved';

// `Promise.any` method
// https://github.com/tc39/proposal-promise-any
$({ target: 'Promise', stat: true }, {
  any: function any(iterable) {
    var C = this;
    var capability = newPromiseCapabilityModule.f(C);
    var resolve = capability.resolve;
    var reject = capability.reject;
    var result = perform(function () {
      var promiseResolve = aFunction(C.resolve);
      var errors = [];
      var counter = 0;
      var remaining = 1;
      var alreadyResolved = false;
      iterate(iterable, function (promise) {
        var index = counter++;
        var alreadyRejected = false;
        errors.push(undefined);
        remaining++;
        promiseResolve.call(C, promise).then(function (value) {
          if (alreadyRejected || alreadyResolved) return;
          alreadyResolved = true;
          resolve(value);
        }, function (e) {
          if (alreadyRejected || alreadyResolved) return;
          alreadyRejected = true;
          errors[index] = e;
          --remaining || reject(new (getBuiltIn('AggregateError'))(errors, PROMISE_ANY_ERROR));
        });
      });
      --remaining || reject(new (getBuiltIn('AggregateError'))(errors, PROMISE_ANY_ERROR));
    });
    if (result.error) reject(result.value);
    return capability.promise;
  }
});


/***/ }),
/* 560 */
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__(561);
var path = __webpack_require__(114);

module.exports = path.Object.values;


/***/ }),
/* 561 */
/***/ (function(module, exports, __webpack_require__) {

var $ = __webpack_require__(1);
var $values = __webpack_require__(562).values;

// `Object.values` method
// https://tc39.github.io/ecma262/#sec-object.values
$({ target: 'Object', stat: true }, {
  values: function values(O) {
    return $values(O);
  }
});


/***/ }),
/* 562 */
/***/ (function(module, exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(11);
var objectKeys = __webpack_require__(94);
var toIndexedObject = __webpack_require__(27);
var propertyIsEnumerable = __webpack_require__(110).f;

// `Object.{ entries, values }` methods implementation
var createMethod = function (TO_ENTRIES) {
  return function (it) {
    var O = toIndexedObject(it);
    var keys = objectKeys(O);
    var length = keys.length;
    var i = 0;
    var result = [];
    var key;
    while (length > i) {
      key = keys[i++];
      if (!DESCRIPTORS || propertyIsEnumerable.call(O, key)) {
        result.push(TO_ENTRIES ? [key, O[key]] : O[key]);
      }
    }
    return result;
  };
};

module.exports = {
  // `Object.entries` method
  // https://tc39.github.io/ecma262/#sec-object.entries
  entries: createMethod(true),
  // `Object.values` method
  // https://tc39.github.io/ecma262/#sec-object.values
  values: createMethod(false)
};


/***/ }),
/* 563 */
/***/ (function(module, exports, __webpack_require__) {

"use strict";
/* WEBPACK VAR INJECTION */(function(global) {

// eslint-disable-next-line func-names
module.exports = function () {
  if (typeof globalThis === 'object') {
    return globalThis;
  }

  var g;

  try {
    // This works if eval is allowed (see CSP)
    // eslint-disable-next-line no-new-func
    g = this || new Function('return this')();
  } catch (e) {
    // This works if the window reference is available
    if (typeof window === 'object') {
      return window;
    } // This works if the self reference is available


    if (typeof self === 'object') {
      return self;
    } // This works if the global reference is available


    if (typeof global !== 'undefined') {
      return global;
    }
  }

  return g;
}();
/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__(24)))

/***/ })
/******/ ]);
});
//# sourceMappingURL=pdfmake.js.map
this.pdfMake = this.pdfMake || {}; this.pdfMake.vfs = {
  "Roboto-Italic.ttf": "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",
  "Roboto-Medium.ttf": "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",
  "Roboto-MediumItalic.ttf": "AAEAAAASAQAABAAgR0RFRrRCsIIAAjW8AAACYkdQT1P5bkuGAAI4IAAAZS5HU1VC64LkWQACnVAAABWQT1MvMpfmsWQAAAGoAAAAYGNtYXABd1geAAAbWAAAEkZjdnQgMRwGTQAAMLQAAABcZnBnbYf8JKsAAC2gAAABvGdhc3AACAATAAI1sAAAAAxnbHlmY+A8RwAAOzAAAfWaaGRteE9zaHUAABZAAAAFGGhlYWT82tJ2AAABLAAAADZoaGVhDNsSswAAAWQAAAAkaG10eHXfmYAAAAIIAAAUOGxvY2GwCyu3AAAxEAAACh5tYXhwBz4DHwAAAYgAAAAgbmFtZaExfDcAAjDMAAAEwXBvc3T/YQBkAAI1kAAAACBwcmVwOd2J9QAAL1wAAAFYAAEAAAACIxJkzjlMXw889QAZCAAAAAAAxPARLgAAAADVAVLw+kH91Ql4CHMAAgAJAAIAAAAAAAAAAQAAB2z+DAAACTf6Qf5iCXgIAAGzAAAAAAAAAAAAAAAABQ4AAQAABQ4AjwAWAFYABQABAAAAAAAOAAACAAI4AAYAAQADBIEB9AAFAAAFmgUzAAABHwWaBTMAAAPRAGYCAAAAAgAAAAAAAAAAAOAAAv9QACBbAAAAIAAAAABHT09HAAEAAP/9BgD+AABmB5oCACAAAZ8AAAAABDoFsAAgACAAAwOWAGQAAAAAAAAAAAH5AAAB+QAAAh8ANwKOAKEExwA7BHMAQgW9ALUFAAAtAVoAkAK/AGgCxv+UA3gAZwRdAD0Bv/+JApYANgI1ADADHP9/BHMAYARzAO8EcwALBHMAJgRzAAkEcwBaBHMAYwRzAIYEcwA7BHMAjgIZACsB4v+aA/wAMgRiAGIEFAAvA9AAlQb7ADIFNP+kBO8AJwUbAGUFHAAnBG0AJwRNACcFUgBrBY0AJwI7ADUEWQADBO4AJwQ9ACcG1QAnBYwAJwVmAGsFAAAnBWYAZATiACcEuQAkBMAAnAUZAFsFDwCbBt4AtwTz/8MExQChBLb/5QIq/+8DSACsAir/egNbAEQDiv95AooAygQ9ACIEaAAQBBoAOARrADsENAA7AsoAXwRw//cEWQANAgUAHwH8/wwEFwARAgUAHwbLABAEWwANBHUAOQRo/8cEcgA7AsQAEAQLABwCnwA7BFoASgPhAGQFzgB3A/H/uQPR/7UD8f/nAqQAMAH9ACACpP+ZBTIAWwIY/+YEZQBMBJv/9gV8AAgEKQAyAff/7ATc/9wDdADRBh4AXgOAAL4DzgBJBFUAgAYeAF4DxwEBAv0A5AQzABsC6QBWAukAZwKRAMgEof/dA9kAfQI7AJ4CCv/TAukA4AOVAL4DzgACBa0AuQYGALEGMACWA9D/0gdV/4cELQAjBWQAFQSvACcEywAbBpcADwSNAEYEeAA9BG8AKgR+/80E0QA7BY4ALgIUACIEfwAhBEwAIQJHAB8FcgAjBG8AEQeDAFAHDQBCAgYAHgVoAE4CxP9KBWkAWwSFADYFgwBbBNYASgIg/w8EJQA2A9IBAgOeAQ0DyAEBA2sA/gIFAQICpwD6AkX/qAPEAN4DEQCsAmP/7gAA/VQAAP3XAAD89gAA/dYAAPy/AAD8oAJVAS4EJQDoAjsAngRdAC4Fnf+qBVoAXgUa/7IEdwAABY8ALgR3/9wFywBSBYkAdQU8AAkEawA+BKv/5QP5AHcEbwA4BEgAKAQFAGYEbwARBIcAbAKgAG4EUv+oA/4APgTXAF0Eb//LBCEAOwR3ADgEFwBuBEgAVwWtADIFpwA/BmsAVASZAFAEYwBtBkoAVwXaAJEFOgBnCFD/yghXAC4GNACgBYkAJQTrACMF4v+EB3H/pQS7AB4FhwAnBYj/ygTtAJsGOABWBdUAJQVoAMUHawArB8YAKwX1AIkGzQAuBOoAIwUxAE8HJgAyBNv/sARlAEMEdgAiA0oAGATa/4UGTv+tBAEAFgR/ABkEVgAiBIH/vwXfACIEfgAZBH8AGQPhAFMFsAA9BLgAGQROAHAGZgAZBsEAEgT6AE8GSAAiBFAAIgQlACMGXAAkBFj/tgRZAA0EJQA7Bqf/vQa5ABkEcAANBH8AGQcbAGAGKQBEBE8AIQb7ACsF3QAZBO//rgRI/5wHFAA+BhAAMAbCABQFwwAWCPUANQfGACIECv+qA9z/tQVaAGIEbwA2BP0AqAP5AHcFWgBmBG8AOAcXAGIGHgBLBxsAYAYpAEQE/QBWBDMARQTjADgAAPzmAAD9DgAA/isAAP48AAD6QQAA+m8GBQAnBP4AGQRPACEE9gAnBG3/xwRSACIDjwARBNYALgQKABEH6P+lBqn/rQWpAC4E3gAiBQ4AIwSRACEGbACkBYMAbAYDACcE8AAZB54AJwWJABEIEQAuBsoAEQYFAGUE4wBLBRv/wwQv/7kHBACdBVAAVgXWAMUEwQBwBVQAuQRbAIUFZgAXBf8AYgSo//QFBAAjBGAAIQYD/8oFAP+/BY0ALgRvABEGDwAnBP0AGQdOACcGUAAiBWgASASMAC8EjP/wBKwAMwOdAAkFSP/DBFz/uQTqADAGxgBFBrIARwYsAKoFCgBhBGMAkgQnAIwHjf/eBnT/3gfKACcGdQALBOcATAQWAD0FiQCQBQAAcwU2AFYGI//KBSD/vwMeAOwD/wAAB/QAAAP/AAAH9AAAAq4AAAIEAAABXAAABGYAAAIpAAABnwAAAQIAAADVAAAAAAAAApcANgKXADYFCwCcBgoAggOK/1IBvQCuAbkAigHK/6QBpQDNAwYAtwMNAJcC+/+hBEUAaQSA//sCwACeA+UAMwWFADMBxABbB3YAnQJpAF0CYP/5A4r/4QLpAGQDTABuBJv/9gZJAA0GjgArCFsAJwYDABwGZAAQBHMATgXRAEIEHgA7BIgAEAU//+QFXf/mBcEAwgPOADEH+QAjBOwA7QT3AH0GDwC2BrIAgganAIgGeQC1BHgARQV1AB8Evv+nBGoAnASYADQIDwBJAib/FwR1ADAEYgBiA/z/1QQUABcD9wA6AlMAaQKOAGYB7P/PBP4AXwSOAEsEogBfBvYAXwb2AF8E9ABfBo0AFwAAAAAH+/+pCDUAXALp/+MC6QBtAukAFwQHAGQEBwAhBAcAWAQGADwEBwAxBAf//AQHAAgEB//xBAcAsgQHADoEFP/WBCUAbQRG/6QF4wCMBGMAcARrADkEJQBiBB4ADgRFAAoEpgA3BFUACgSmADoEwgAKBeIACgOmAAoERAAKA8L/8gH3ABgEwwAKBIwAPwOyAAoEHgAOBE4AEQOSAAMDpgAKBGP/pwSmADoEY/+nA4b/1wS9AAoEBP/ZBXwAQwU1AHAEyAAABXAAYgRnADkHJv/BBykACgV2AGIEvAAKBFkACwU6/4MGFP+pBC8ADATIAAsERQAKBLD/wQQvAHIFPgAKBHMAXQZcAAoG3gAKBTsASgX7AAsETwALBGcAEwZqAAoEb//SBAz/9QZq/6kEhAAKBP0ACgVOAGIFzABABEMAbQSp/6QGbABiBHMAXQRzAAoF2gA3BLcANAQvAAwEpgA6BE4ABAPjAB4IAQAKBM//2QLp//QC6f/xAukACgLpABYC6QAjAukACALpADMDpgCTAqcBBgPMAAoEJf+HBKEAOwUmAC4FCgAuBBoAFAUbAC4EFwAUBGIACgRnADkESAAKBIX/mwH/AOsDjwEEAAD9JgP2ANwD9gATA/YA2AP2ANcDpgAKA48BBAOPAQUC6QCKAukAZALpAH0C6QCJAukAlgLpAHsC6QCmBVoAbwWBAHEFZgAuBb0AcgW/AHIEBQCrBGkAHwRA/38EpP/RBFP/1AQYACcDjwEEAbD/sgZvADoEmgBHAgr/BwRz/6gEc//YBHP/yARzABcEcwBGBHMAIgRzAF0EcwAuBHMAOwRzAPUCIP8PAiD/DwIVACICFf99AhUAIgRIAAoEzwBOBB0AVQRvABAEJgA3BH8AOwR6ACoEhwA0BHv/xwSGADsENAA7BHAAMAOSAKQE5wAiA7H/7QYg/4ID9AAKBKb/3ATsAB4EwgAKAfkAAAKWADYFOv//BTr//wR3ACsEwACcAp//4gU0/6QFNP+kBTT/pAU0/6QFNP+kBTT/pAU0/6QFGwBlBG0AJwRtACcEbQAnBG0AJwI7ADUCOwA1AjsANQI7ADUFjAAnBWYAawVmAGsFZgBrBWYAawVmAGsFGQBbBRkAWwUZAFsFGQBbBMUAoQQ9ACIEPQAiBD0AIgQ9ACIEPQAiBD0AIgQ9ACIEGgA4BDQAOwQ0ADsENAA7BDQAOwIUACICFAAiAhQAIgIUACIEWwANBHUAOQR1ADkEdQA5BHUAOQR1ADkEWgBKBFoASgRaAEoEWgBKA9H/tQPR/7UFNP+kBD0AIgU0/6QEPQAiBTT/pAQ9ACIFGwBlBBoAOAUbAGUEGgA4BRsAZQQaADgFGwBlBBoAOAUcACcFAQA7BG0AJwQ0ADsEbQAnBDQAOwRtACcENAA7BG0AJwQ0ADwEbQAnBDQAOwVSAGsEcP/3BVIAawRw//cFUgBrBHD/9wVSAGsEcP/3BY0AJwRZAA0COwA1AhQAFAI7ADUCFAAiAjsANQIUACICO/+OAgX/dgI7ADUGlAA1BAEAHwRZAAMCIP8PBO4AJwQXABEEPQAnAgUAHwQ9ACcCBf+iBD0AJwKbAB8EPQAnAuEAHwWMACcEWwANBYwAJwRbAA0FjAAnBFsADQRbAA0FZgBrBHUAOQVmAGsEdQA5BWYAawR1ADkE4gAnAsQAEATiACcCxP+cBOIAJwLEABAEuQAkBAsAHAS5ACQECwAcBLkAJAQLABwEuQAkBAsAHAS5ACQECwAcBMAAnAKfADsEwACcAp8AOwTAAJwCxwA7BRkAWwRaAEoFGQBbBFoASgUZAFsEWgBKBRkAWwRaAEoFGQBbBFoASgUZAFsEWgBKBt4AtwXOAHcExQChA9H/tQTFAKEEtv/lA/H/5wS2/+UD8f/nBLb/5QPx/+cHVf+HBpcADwVkABUEbwAqBGL/lwRi/5cEJQBiBIX/mwSF/5sEhf+bBIX/mwSF/5sEhf+bBIX/mwRnADkDzAAKA8wACgPMAAoDzAAKAfcAGAH3ABgB9wAYAfcAGATCAAoEpgA6BKYAOgSmADoEpgA6BKYAOgRrADkEawA5BGsAOQRrADkEJQBtBIX/mwSF/5sEhf+bBGcAOQRnADkEZwA5BGcAOQRiAAoDzAAKA8wACgPMAAoDzAAKA8wACgSMAD8EjAA/BIwAPwSMAD8EwwAKAfcADQH3ABgB9wAYAff/igH3ABgDwv/yBEQACgOmAAoDpgAKA6YACgOmAAoEwgAKBMIACgTCAAoEpgA6BKYAOgSmADoERQAKBEUACgRFAAoEHgAOBB4ADgQeAA4EHgAOBCUAYgQlAGIEJQBiBGsAOQRrADkEawA5BGsAOQRrADkEawA6BeMAjAQlAG0EJQBtBBT/1gQU/9YEFP/WBTT/pARt/14Fjf9mAjv/agV6ABgFKf9YBVAAHQKgAAsFNP+kBO8AJwRtACcEtv/lBY0AJwI7ADUE7gAnBtUAJwWMACcFZgBrBQAAJwTAAJwExQChBPP/wwI7ADUExQChBGsAPgRIACgEbwARAqAAbgRIAFcEfwAhBHUAOQSh/90D4QBkA/H/uQKgAEwESABXBHUAOQRIAFcGawBUBG0AJwRdAC4EuQAkAjsANQI7ADUEWQADBQoALgTuACcE7QCbBTT/pATvACcEXQAuBG0AJwWHACcG1QAnBY0AJwVmAGsFjwAuBQAAJwUbAGUEwACcBPP/wwQ9ACIENAA7BH8AGQR1ADkEaP/HBBoAOAPR/7UD8f+5BDQAOwNKABgECwAcAgUAHwIUACIB/P8MBFYAIgPR/7UG3gC3Bc4AdwbeALcFzgB3Bt4AtwXOAHcExQChA9H/tQFaAJACjgChBD4ANwIg/w8BuQCKBtUAJwbLABAFNP+kBD0AIgRtACcFhwAnBDQAOwR/ABkFiQB1BacAPwT9AKgD+QB3CEYAOQk3AGsEuwAeBAEAFgUbAGUEGgA4BMUAoQP5AHcCOwA1B3H/pQZO/60COwA1BTT/pAQ9ACIFNP+kBD0AIgdV/4cGlwAPBG0AJwQ0ADsFaABIBCUANgQlADYHcf+lBk7/rQS7AB4EAQAWBYcAJwR/ABkFhwAnBH8AGQVmAGsEdQA5BVoAYgRvADYFWgBiBG8ANgUxAE8EJQAjBO0AmwPR/7UE7QCbA9H/tQTtAJsD0f+1BWgAxQROAHAGzQAuBkgAIgRrADsFNP+kBD0AIgU0/6QEPQAiBTT/pAQ9ACIFNP+kBD0AIgU0/6QEPQAiBTT/pAQ9ACIFNP+kBD0AIgU0/6QEPQAiBTT/pAQ9ACIFNP+kBD0AIgU0/6QEPQAiBTT/pAQ9ACIEbQAnBDQAOwRtACcENAA7BG0AJwQ0ADsEbQAnBDQAOwRtACcENAA7BG0AJwQ0ADsEbQAnBDQAOwRtACcENAA7AjsANQIUACICO///AgX/5AVmAGsEdQA5BWYAawR1ADkFZgBrBHUAOQVmAGsEdQA5BWYAawR1ADkFZgBrBHUAOQVmAGsEdQA5BWkAWwSFADYFaQBbBIUANgVpAFsEhQA2BWkAWwSFADYFaQBbBIUANgUZAFsEWgBKBRkAWwRaAEoFgwBbBNYASgWDAFsE1gBKBYMAWwTWAEoFgwBbBNYASgWDAFsE1gBKBMUAoQPR/7UExQChA9H/tQTFAKED0f+1BIn/9ATAAJwD4QBTBWgAxQROAHAEXQAuA0oAGAX/AGIEqP/0BFkADQTqACME6gAjBF3//ANK/8sFHgA3BC4AGQTFAKED+QBUBPP/wwPx/7kESAAoBE3/wwYKAIIEcwALBHMAJgRzAAkEcwBaBIcAdwSbAE8EhwCOBJsAdAVSAGsEcP/3BYwAJwRbAA0FNP+kBD0AIgRtACcENAA7Ajv/yQIU/34FZgBrBHUAOQTiACcCxAAHBRkAWwRaAEoEx/8kBO8AJwRoABAFHAAnBGsAOwUcACcEawA7BY0AJwRZAA0E7gAnBBcAEQTuACcEFwARBD0AJwIF/+QG1QAnBssAEAWMACcEWwANBWYAawUAACcEaP/HBOIAJwLE/94EuQAkBAsAHATAAJwCnwA7BRkAWwUPAJsD4QBkBQ8AmwPhAGQG3gC3Bc4AdwS2/+UD8f/nBaz+swSF/5sECP9tBP//eAIz/3sEsP/SBGH/LATS/+IEhf+bBEgACgPMAAoEFP/WBMMACgH3ABgERAAKBeIACgTCAAoEpgA6BFUACgQlAGIEJQBtBEb/pAH3ABgEJQBtA8wACgOmAAoEHgAOAfcAGAH3ABgDwv/yBEQACgQvAHIEhf+bBEgACgOmAAoDzAAKBMgACwXiAAoEwwAKBKYAOgS9AAoEVQAKBGcAOQQlAGIERv+kBC8ADQTDAAoEZwA6BCUAbQXaADcEyAALBC8AcgV8AEMFuQAYBiD/ggSm/9wEHgAOBeMAjAXjAIwF4wCMBCUAbQU0/6QEPQAiBG0AJwQ0ADsEhf+bA8wACgIU/+QAAAABAAAFEAkKBAAAAgICAwUFBgYCAwMEBQIDAgQFBQUFBQUFBQUFAgIEBQUECAYGBgYFBQYGAwUGBQgGBgYGBgUFBgYIBgUFAgQCBAQDBQUFBQUDBQUCAgUCCAUFBQUDBQMFBAcEBAQDAgMGAgUFBgUCBQQHBAQFBwQDBQMDAwUEAwIDBAQGBwcECAUGBQUHBQUFBQUGAgUFAwYFCAgCBgMGBQYFAgUEBAQEAgMDBAMDAAAAAAAAAwUDBQYGBgUGBQcGBgUFBAUFBQUFAwUEBQUFBQUFBgYHBQUHBwYJCQcGBgcIBQYGBgcHBggJBwgGBggFBQUEBQcFBQUFBwUFBAYFBQcIBgcFBQcFBQUHCAUFCAcFCAcGBQgHCAYKCQUEBgUGBAYFCAcIBwYFBgAAAAAAAAcGBQYFBQQFBQkIBgUGBQcGBwYJBgkIBwYGBQgGBwUGBQYHBQYFBwYGBQcGCAcGBQUFBAYFBggIBwYFBQkHCQcGBQYGBgcGBAUJBQkDAgIFAgIBAQADAwYHBAICAgIDAwMFBQMEBgIIAwMEAwQFBwcJBwcFBwUFBgYGBAkGBgcIBwcFBgUFBQkCBQUEBQQDAwIGBQUICAYHAAkJAwMDBQUFBQUFBQUFBQUFBQcFBQUFBQUFBQUHBAUEAgUFBAUFBAQFBQUEBQUGBgUGBQgIBgUFBgcFBQUFBQYFBwgGBwUFBwUFBwUGBgcFBQcFBQcFBQUFBAkFAwMDAwMDAwQDBAUFBgYFBgUFBQUFAgQABAQEBAQEBAMDAwMDAwMGBgYGBgUFBQUFBQQCBwUCBQUFBQUFBQUFBQICAgICBQUFBQUFBQUFBQUFBAYEBwQFBgUCAwYGBQUDBgYGBgYGBgYFBQUFAwMDAwYGBgYGBgYGBgYFBQUFBQUFBQUFBQUFAgICAgUFBQUFBQUFBQUEBAYFBgUGBQYFBgUGBQYFBgYFBQUFBQUFBQUFBgUGBQYFBgUGBQMCAwIDAgMCAwcFBQIGBQUCBQIFAwUDBgUGBQYFBQYFBgUGBQYDBgMGAwUFBQUFBQUFBQUFAwUDBQMGBQYFBgUGBQYFBgUIBwUEBQUEBQQFBAgHBgUFBQUFBQUFBQUFBQQEBAQCAgICBQUFBQUFBQUFBQUFBQUFBQUFBQQEBAQEBQUFBQUCAgICAgQFBAQEBAUFBQUFBQUFBQUFBQUFBQUFBQUFBQUHBQUFBQUGBQYDBgYGAwYGBQUGAwYIBgYGBQUGAwUFBQUDBQUFBQQEAwUFBQcFBQUDAwUGBgYGBgUFBggGBgYGBgUGBQUFBQUFBAQFBAUCAgIFBAgHCAcIBwUEAgMFAgIICAYFBQYFBQYGBgQJCgUFBgUFBAMIBwMGBQYFCAcFBQYFBQgHBQUGBQYFBgUGBQYFBgUGBAYEBgQGBQgHBQYFBgUGBQYFBgUGBQYFBgUGBQYFBgUGBQUFBQUFBQUFBQUFBQUFBQUDAgMCBgUGBQYFBgUGBQYFBgUGBQYFBgUGBQYFBgUGBQYFBgUGBQYFBgUFBAUEBQQFBQQGBQUEBwUFBgYFBAYFBQQGBAUFBwUFBQUFBQUFBgUGBQYFBQUDAgYFBgMGBQUGBQYFBgUGBQYFBgUFAggIBgUGBgUGAwUFBQMGBgQGBAgHBQQGBQUGAgUFBQUFBAUFAgUHBQUFBQUFAgUEBAUCAgQFBQUFBAQFBwUFBQUFBQUFBQUFBwUFBgYHBQUHBwcFBgUFBQUEAgAAAAMAAAADAAAAHAADAAEAAAAcAAMACgAABooABAZuAAAA9ACAAAYAdAAAAAIADQB+AKAArACtAL8AxgDPAOYA7wD+AQ8BEQElAScBMAFTAV8BZwF+AX8BjwGSAaEBsAHwAf8CGwI3AlkCvALHAskC3QLzAwEDAwMJAw8DIwOKA4wDkgOhA7ADuQPJA84D0gPWBCUELwRFBE8EYgRvBHkEhgSfBKkEsQS6BM4E1wThBPUFAQUQBRMeAR4/HoUe8R7zHvkfTSAJIAsgESAVIB4gIiAnIDAgMyA6IDwgRCB0IH8gpCCqIKwgsSC6IL0hBSETIRYhIiEmIS4hXiICIgYiDyISIhoiHiIrIkgiYCJlJcruAvbD+wT+///9//8AAAAAAAIADQAgAKAAoQCtAK4AwADHANAA5wDwAP8BEAESASYBKAExAVQBYAFoAX8BjwGSAaABrwHwAfoCGAI3AlkCvALGAskC2ALzAwADAwMJAw8DIwOEA4wDjgOTA6MDsQO6A8oD0QPWBAAEJgQwBEYEUARjBHAEegSIBKAEqgSyBLsEzwTYBOIE9gUCBREeAB4+HoAeoB7yHvQfTSAAIAogECATIBcgICAlIDAgMiA5IDwgRCB0IH8goyCmIKsgsSC5ILwhBSETIRYhIiEmIS4hWyICIgYiDyIRIhoiHiIrIkgiYCJkJcruAfbD+wH+///8//8AAQAA//b/5AHY/8IBzP/BAAABvwAAAboAAAG2AAABtAAAAbIAAAGqAAABrP8W/wf/Bf74/usB7gAAAAD+Zf5EASP92P3X/cn9tP2o/af9ov2d/YoAAP/+//0AAAAA/QoAAP/e/P78+wAA/LoAAPyyAAD8pwAA/KEAAPyZAAD8kQAA/ygAAP8lAAD8XgAA5eLlouVT5X7k5+V85X3hcuFz4W8AAOFs4WvhaeFh46nhWeOh4VDhIeEXAADg8gAA4O3g5uDl4J7gkeCP4ITflOB54E3fqt6s357fnd+W35Pfh99r31TfUdvtE7cK9wa7AsMBxwABAAAAAAAAAAAAAAAAAAAAAADkAAAA7gAAARgAAAEyAAABMgAAATIAAAF0AAAAAAAAAAAAAAAAAAABdAF+AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWwAAAAAAXQBkAAAAagAAAAAAAABwAAAAggAAAIwAAACUgAAAmIAAAKOAAACmgAAAr4AAALOAAAC4gAAAAAAAAAAAAAAAAAAAAAAAAAAAtIAAAAAAAAAAAAAAAAAAAAAAAAAAALCAAACwgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJ/AoACgQKCAoMChACBAnsCjwKQApECkgKTApQAggCDApUClgKXApgCmQCEAIUCmgKbApwCnQKeAp8AhgCHAqoCqwKsAq0CrgKvAIgAiQKwArECsgKzArQAigJ6AIsAjAJ8AI0C4wLkAuUC5gLnAugAjgLpAuoC6wLsAu0C7gLvAvAAjwCQAvEC8gLzAvQC9QL2AvcAkQCSAvgC+QL6AvsC/AL9AJMAlAMMAw0DEAMRAxIDEwJ9An4ChQKgAysDLAMtAy4DCgMLAw4DDwCuAK8DhgCwA4cDiAOJALEAsgOQA5EDkgCzA5MDlAC0A5UDlgC1A5cAtgOYALcDmQOaALgDmwC5ALoDnAOdA54DnwOgA6EDogOjAMQDpQOmAMUDpADGAMcAyADJAMoAywDMA6cAzQDOA+QDrQDSA64A0wOvA7ADsQOyANQA1QDWA7QD5QO1ANcDtgDYA7cDuADZA7kA2gDbANwDugOzAN0DuwO8A70DvgO/A8ADwQDeAN8DwgPDAOoA6wDsAO0DxADuAO8A8APFAPEA8gDzAPQDxgD1A8cDyAD2A8kA9wPKA+YDywECA8wBAwPNA84DzwPQAQQBBQEGA9ED5wPSAQcBCAEJBIED6APpARcBGAEZARoD6gPrA+0D7AEoASkBKgErBIABLAEtAS4BLwEwBIIEgwExATIBMwE0A+4D7wE1ATYBNwE4BIQEhQPwA/EEdwR4A/ID8wSGBIcEfwFMAU0EfQR+A/QD9QP2AU4BTwFQAVEBUgFTAVQBVQR5BHoBVgFXAVgEAQQABAIEAwQEBAUEBgFZAVoEewR8BBsEHAFbAVwBXQFeBIgEiQFfBB0EigFvAXABgQGCBIwEiwGXBHYBnQAMAAAAAAu8AAAAAAAAAPkAAAAAAAAAAAAAAAEAAAACAAAAAgAAAAIAAAANAAAADQAAAAMAAAAgAAAAfgAAAAQAAACgAAAAoAAAAngAAAChAAAArAAAAGMAAACtAAAArQAAAnkAAACuAAAAvwAAAG8AAADAAAAAxQAAAn8AAADGAAAAxgAAAIEAAADHAAAAzwAAAoYAAADQAAAA0AAAAnsAAADRAAAA1gAAAo8AAADXAAAA2AAAAIIAAADZAAAA3QAAApUAAADeAAAA3wAAAIQAAADgAAAA5QAAApoAAADmAAAA5gAAAIYAAADnAAAA7wAAAqEAAADwAAAA8AAAAIcAAADxAAAA9gAAAqoAAAD3AAAA+AAAAIgAAAD5AAAA/QAAArAAAAD+AAAA/gAAAIoAAAD/AAABDwAAArUAAAEQAAABEAAAAnoAAAERAAABEQAAAIsAAAESAAABJQAAAsYAAAEmAAABJgAAAIwAAAEnAAABJwAAAnwAAAEoAAABMAAAAtoAAAExAAABMQAAAI0AAAEyAAABNwAAAuMAAAE4AAABOAAAAI4AAAE5AAABQAAAAukAAAFBAAABQgAAAI8AAAFDAAABSQAAAvEAAAFKAAABSwAAAJEAAAFMAAABUQAAAvgAAAFSAAABUwAAAJMAAAFUAAABXwAAAv4AAAFgAAABYQAAAwwAAAFiAAABZQAAAxAAAAFmAAABZwAAAn0AAAFoAAABfgAAAxQAAAF/AAABfwAAAJUAAAGPAAABjwAAAJYAAAGSAAABkgAAAJcAAAGgAAABoQAAAJgAAAGvAAABsAAAAJoAAAHwAAAB8AAAA94AAAH6AAAB+gAAAoUAAAH7AAAB+wAAAqAAAAH8AAAB/wAAAysAAAIYAAACGQAAAwoAAAIaAAACGwAAAw4AAAI3AAACNwAAAJwAAAJZAAACWQAAAJ0AAAK8AAACvAAAA98AAALGAAACxwAAAJ4AAALJAAACyQAAAKAAAALYAAAC3QAAAKEAAALzAAAC8wAAAKcAAAMAAAADAQAAAKgAAAMDAAADAwAAAKoAAAMJAAADCQAAAKsAAAMPAAADDwAAAKwAAAMjAAADIwAAAK0AAAOEAAADhQAAAK4AAAOGAAADhgAAA4YAAAOHAAADhwAAALAAAAOIAAADigAAA4cAAAOMAAADjAAAA4oAAAOOAAADkgAAA4sAAAOTAAADlAAAALEAAAOVAAADlwAAA5AAAAOYAAADmAAAALMAAAOZAAADmgAAA5MAAAObAAADmwAAALQAAAOcAAADnQAAA5UAAAOeAAADngAAALUAAAOfAAADnwAAA5cAAAOgAAADoAAAALYAAAOhAAADoQAAA5gAAAOjAAADowAAALcAAAOkAAADpQAAA5kAAAOmAAADpgAAALgAAAOnAAADpwAAA5sAAAOoAAADqQAAALkAAAOqAAADsAAAA5wAAAOxAAADuQAAALsAAAO6AAADugAAA6MAAAO7AAADuwAAAMQAAAO8AAADvQAAA6UAAAO+AAADvgAAAMUAAAO/AAADvwAAA6QAAAPAAAADxgAAAMYAAAPHAAADxwAAA6cAAAPIAAADyQAAAM0AAAPKAAADzgAAA6gAAAPRAAAD0gAAAM8AAAPWAAAD1gAAANEAAAQAAAAEAAAAA+QAAAQBAAAEAQAAA60AAAQCAAAEAgAAANIAAAQDAAAEAwAAA64AAAQEAAAEBAAAANMAAAQFAAAECAAAA68AAAQJAAAECwAAANQAAAQMAAAEDAAAA7QAAAQNAAAEDQAAA+UAAAQOAAAEDgAAA7UAAAQPAAAEDwAAANcAAAQQAAAEEAAAA7YAAAQRAAAEEQAAANgAAAQSAAAEEwAAA7cAAAQUAAAEFAAAANkAAAQVAAAEFQAAA7kAAAQWAAAEGAAAANoAAAQZAAAEGQAAA7oAAAQaAAAEGgAAA7MAAAQbAAAEGwAAAN0AAAQcAAAEIgAAA7sAAAQjAAAEJAAAAN4AAAQlAAAEJQAAA8IAAAQmAAAELwAAAOAAAAQwAAAEMAAAA8MAAAQxAAAENAAAAOoAAAQ1AAAENQAAA8QAAAQ2AAAEOAAAAO4AAAQ5AAAEOQAAA8UAAAQ6AAAEPQAAAPEAAAQ+AAAEPgAAA8YAAAQ/AAAEPwAAAPUAAARAAAAEQQAAA8cAAARCAAAEQgAAAPYAAARDAAAEQwAAA8kAAAREAAAERAAAAPcAAARFAAAERQAAA8oAAARGAAAETwAAAPgAAARQAAAEUAAAA+YAAARRAAAEUQAAA8sAAARSAAAEUgAAAQIAAARTAAAEUwAAA8wAAARUAAAEVAAAAQMAAARVAAAEWAAAA80AAARZAAAEWwAAAQQAAARcAAAEXAAAA9EAAARdAAAEXQAAA+cAAAReAAAEXgAAA9IAAARfAAAEYQAAAQcAAARiAAAEYgAABIEAAARjAAAEbwAAAQoAAARwAAAEcQAAA+gAAARyAAAEdQAAARcAAAR2AAAEdwAAA+oAAAR4AAAEeAAAA+0AAAR5AAAEeQAAA+wAAAR6AAAEhgAAARsAAASIAAAEiwAAASgAAASMAAAEjAAABIAAAASNAAAEkQAAASwAAASSAAAEkwAABIIAAASUAAAElwAAATEAAASYAAAEmQAAA+4AAASaAAAEnQAAATUAAASeAAAEnwAABIQAAASgAAAEqQAAATkAAASqAAAEqwAAA/AAAASsAAAErQAABHcAAASuAAAErwAAA/IAAASwAAAEsQAABIYAAASyAAAEugAAAUMAAAS7AAAEuwAABH8AAAS8AAAEvQAAAUwAAAS+AAAEvwAABH0AAATAAAAEwgAAA/QAAATDAAAEygAAAU4AAATLAAAEzAAABHkAAATNAAAEzgAAAVYAAATPAAAE1wAAA/cAAATYAAAE2AAAAVgAAATZAAAE2QAABAEAAATaAAAE2gAABAAAAATbAAAE3wAABAIAAATgAAAE4QAAAVkAAATiAAAE9QAABAcAAAT2AAAE9wAABHsAAAT4AAAE+QAABBsAAAT6AAAE/QAAAVsAAAT+AAAE/wAABIgAAAUAAAAFAAAAAV8AAAUBAAAFAQAABB0AAAUCAAAFEAAAAWAAAAURAAAFEQAABIoAAAUSAAAFEwAAAW8AAB4AAAAeAQAAA+IAAB4+AAAePwAAA+AAAB6AAAAehQAAA9MAAB6gAAAe8QAABB4AAB7yAAAe8wAAA9kAAB70AAAe+QAABHAAAB9NAAAfTQAABMoAACAAAAAgCQAAAXIAACAKAAAgCwAAAX0AACAQAAAgEQAAAX8AACATAAAgFAAAAYEAACAVAAAgFQAABIwAACAXAAAgHgAAAYMAACAgAAAgIgAAAYsAACAlAAAgJwAAAY4AACAwAAAgMAAAAZEAACAyAAAgMwAAA9sAACA5AAAgOgAAAZIAACA8AAAgPAAAA90AACBEAAAgRAAAAZQAACB0AAAgdAAAAZUAACB/AAAgfwAAAZYAACCjAAAgowAABIsAACCkAAAgpAAAAZcAACCmAAAgqgAAAZgAACCrAAAgqwAABHYAACCsAAAgrAAAAZ0AACCxAAAgsQAAAZ4AACC5AAAgugAAAZ8AACC8AAAgvQAAAaEAACEFAAAhBQAAAaMAACETAAAhEwAAAaQAACEWAAAhFgAAAaUAACEiAAAhIgAAAaYAACEmAAAhJgAAALoAACEuAAAhLgAAAacAACFbAAAhXgAAAagAACICAAAiAgAAAawAACIGAAAiBgAAALIAACIPAAAiDwAAAa0AACIRAAAiEgAAAa4AACIaAAAiGgAAAbAAACIeAAAiHgAAAbEAACIrAAAiKwAAAbIAACJIAAAiSAAAAbMAACJgAAAiYAAAAbQAACJkAAAiZQAAAbUAACXKAAAlygAAAbcAAO4BAADuAgAAAbgAAPbDAAD2wwAAAboAAPsBAAD7BAAAAbwAAP7/AAD+/wAAAcIAAP/8AAD//QAAAcMAALAALEuwCVBYsQEBjlm4Af+FsEQdsQkDX14tsAEsICBFaUSwAWAtsAIssAEqIS2wAywgRrADJUZSWCNZIIogiklkiiBGIGhhZLAEJUYgaGFkUlgjZYpZLyCwAFNYaSCwAFRYIbBAWRtpILAAVFghsEBlWVk6LbAELCBGsAQlRlJYI4pZIEYgamFksAQlRiBqYWRSWCOKWS/9LbAFLEsgsAMmUFhRWLCARBuwQERZGyEhIEWwwFBYsMBEGyFZWS2wBiwgIEVpRLABYCAgRX1pGESwAWAtsAcssAYqLbAILEsgsAMmU1iwQBuwAFmKiiCwAyZTWCMhsICKihuKI1kgsAMmU1gjIbDAioobiiNZILADJlNYIyG4AQCKihuKI1kgsAMmU1gjIbgBQIqKG4ojWSCwAyZTWLADJUW4AYBQWCMhuAGAIyEbsAMlRSMhIyFZGyFZRC2wCSxLU1hFRBshIVktsAossCxFLbALLLAtRS2wDCyxJwGIIIpTWLlAAAQAY7gIAIhUWLkALAPocFkbsCNTWLAgiLgQAFRYuQAsA+hwWVlZLbANLLBAiLggAFpYsS0ARBu5AC0D6ERZLbAMK7AAKwCyAQ4CKwGyDwECKwG3DzowJRsQAAgrALcBOC4kGhEACCu3Ak5AMiMVAAgrtwNIOy4hFAAIK7cETkAyIxUACCu3BTAoHxYOAAgrtwZjUT8tGwAIK7cHQDQkGhEACCu3CFtKOikZAAgrtwmDZE46IwAIK7cKd2JMNiEACCu3C5F3XDojAAgrtwx2YEs2HQAIK7cNLCQcFAwACCu3DkM3Kx8SAAgrALIQDgcrsAAgRX1pGESygBQBc7KwFAFzslAUAXSygBQBdLJwFAF1sj8cAXOyXxwBc7J/HAFzsi8cAXSyTxwBdLJvHAF0so8cAXSyrxwBdLL/HAF0sh8cAXWyPxwBdbJfHAF1sn8cAXWyDyABc7JvIAF1sn8gAXOy7yABc7IfIAF0sl8gAXSyjyABdLLPIAF0sv8gAXSyPyABdbIvIgFzsm8iAXOyLyoBc7I/KgFzACoAzACRAJ4AkQDsAHIAsgB9AFYAXwBOAGABBACqAMQAAAAU/mAAFAKbABD/OQAN/pcAEgMhAAsEOgAWBI0AEAWwABQGGAAVBsAAEAJbABIHBAAFBt4AAQAAAAAAAABgAGAAYABgAGAAnQDIAUcB0QKBAxUDMANfA4oDvQPlBAUEHARFBFwEvgTtBUcFwwYJBnYG6QcXB6EIEggeCCoISwh0CJUJBQnDCgoKegrbCywLcAupDBUMWQx0DK8M+A0dDXcNtQ4aDmsO1w80D6sP1xAdEE4QnRDnERkRVBF7EZIRuhHhEf0SHRKrExcTbhPXFFUUrhU8FYcVxRYYFmEWfRb2F0YXohgQGIIYwhk9GZYZ5BoUGmMaqxrwGysbehuRG98cJRxjHMgdNx2lHgoeKx7NHwMfuiA1IEEgYCEmIUEhhCHKIiQimyK7Iw4jPSNiI5UjxSQbJCckQSRbJHUk5yVOJYomCSZiJtgnwCg7KJkpGCl8KgEqZSqAKs4rGytbK7csGyyrLWstoS4VLoIu/y91L9MwOjBpMNUxEDE4MUAxcTGXMdcyBTJNMoMyyjLpMwkzEjNEM3YzlzOwNAI0CjQyNF806jUXNVo1ijXJNkU2pjcWN6E4HThRONM5ZjnEOhQ6nDrOOyo7ojv7PF88yT06PYM9zz5APqM/Hz+gP/tAe0DkQWRB5EJdQrlC+UNXQ7FEH0SoRONFMEV2RfdGMEZ3RrhHBUdiR8tIHUieSSxJjUoMSo9Kt0sRS4BMF0xSTKVM7006TZdNx031TpZOzk8WT1dPn0/7UGJQslEmUbJSElKTUv1TeFPwVFtUmlULVXhV6FZ0VwNXUFefWAxYfFj1WWhZ+1qMWytb21xaXMxdEV1XXcxeO18YX+9ggGERYW9hymH+YhtiVWJsYoNjYGPPY+pkBWR1ZNJlT2WAZatmCWZZZmVmcWZ9Zolm42c6Z41n5WfxZ/1oS2idaQRpX2oMaqlqtWrBawtrUmtea2prwWwSbFls3W1sbc9uK243bkNup28GbxJvHm8qbzZvqnATcHZwhXCVcKFwrXEBcWxyBnKEcv1zcnPidFd0x3U7da52EHZodsJ3Gnehd613uXfsd+x37Hfsd+x37Hfsd+x37Hfsd+x37Hfsd+x39Hf8eAZ4EHhCeGR4hnimeMh41HjgeRR5VXm7eeF57Xn9eix7GHs0e1F7ZHt4e8N8VHz+fZt9p359ful/coAwgJWBF4F3ge6CoYMPg6+EEYR8hJaEsITKhOSFX4WIhcOF2oYPhq+G94dwh7KHwYfQiAmIFohEiGGIbYjdiUCJ6oqIixaL74vvjbmOIo57jqaO+Y9oj/aQKJCXkP6RSpHWkjSSZpLCkv2TLpN4k8OT85Q3lGOU3JU4lZyV65ZNloeW2pb/l0SXe5eWl/OYYZidmR6ZipnumhmaT5rNmv6bS5t/m8ScNpyXnQOdZ53jnlme0Z8qn2yfyqAioJ+hAaE/oZmh3qIoomSirKLnoymjh6OTo+akX6TtpUylkqYbpoOm7adTp+yn+KhLqJuo76k3qayqFqp9qv+rmawkrMitS63BrgaucK7ZrwevmLAHsB6wc7C6sYGx+bJxsryzBbNJs4yz27Q4tMm1B7UmtXS15bYstnK20LdHt3W3xbgnuDu4T7hhuHW4h7ieuLK5E7mEudW6Qbqxut27ObuXu+G8RbxsvOK8+L2Fvfi+Kr4yvjq+Tb5Vvma+br52vn6+hr6Ovpa+nr6mvrG+ub8pv4i/tsAkwH3A6MFzwdXCP8KuwynDssO6xETEgsTxxUfFyMY9xpHGkcaZxwfHdcfgyCfInMizyMrI4cjzyP/JEskeySrJQclYyW/Je8mSyanJwMnMyd7J9coHyhnKK8o3yk7KZcp8yojKn8q2ys3K48r5ywXLHcspyzXLTMtjy3nLhMuay7DLx8vTy+nMAMwXzC3MQ8xOzGXMd8yIzJTMpsyxzMjM2czrzP3Nbs4ezjDOQs5UzmXOd86JzpvOrM7Dzs/O5s73zwnPGs8szz7PtNBV0GzQfdCP0KDQstDD0NXQ59Dz0QXRHNEu0UDRUtFp0YDRktGk0a/RutHM0djR5NH20g3SGdIl0jzSU9Jf0mvSgNKQ0pzSqNK/0tHS3dLp0wDTEdMh0zPTRNNW02jTe9OO06DTstO+08rT3NPt0//UEdQj1DTUQNRM1FjUZNR21IfUk9Sf1KvUt9TO1NrU7NT91Q/VINUy1UTVV9Vq1X3VkNX81nbWjdak1rvW0dbd1vTXC9cd1y/XQddS12nXe9eS16nX1Nf/2A/YJtg92FPYZNhw2IPYj9ib2LLYydjf2OrZANkW2S3ZOdlL2WLZdNmF2ZfZo9m62czZ3dnp2gDaFtoo2p3ar9rA2tLa49r52w/bINsy26jbvtvP2+Hb89v/3BXcJ9w+3FDcW9xs3IPcj9yl3LHcxtzS3Onc9d0M3R3dL91C3VTdYN1x3YPdlN2g3bHdvd3T3d/d8N4B3hPeJt453qTeu97R3tze898F3xvfJt8y3z7fSt9W32Lfbt+E34zflN+c36TfrN+037zfxN/M39Tf3N/k3+zf9OAA4AzgHuAw4ELgU+Bo4HDgeOCA4Ijg7+D74QbhGOEq4TzhSOFf4dbh3uHq4fLh+uIR4iPiK+Iz4jviQ+JV4l3iZeJt4nXifeKF4o3ileKd4qXit+K/4sfjHuMm4y7jOeNQ41jjYONs43Tji+Oc47PjyuPh4/jkBOQQ5CfkPeRM5GPkb+SB5I3kpOS25MLkzuTl5PzlE+Uq5TLlOuVS5WrlduWC5Y7lmuWm5bLluuXC5crl3OXu5fbmCOYa5ibmMuY65kLmVOZl5nHmeeaE5pDmnOao5rPmyubg5uzm+OcE5w/nF+cf5yvnNudC503nX+dw53znh+ea57LnvufJ59nn6efx5/3oCegb6C3oRehc6HToi+ij6Lro0ujp6QTpHuk26U3pYOly6YXpl+mq6bzp1+ny6f7qCuoc6i7qQOpR6mnqgOqY6q/qx+re6vbrDeso60LrVOtm63LrfuuK65brqOu669Lr6ewB7BjsMOxH7F/sduyR7KvswuzZ7PDtB+0e7TXtTO1i7W7teu2G7ZLtqe3A7dft7u4F7hzuM+5K7mHud+6D7o/um+6n7rnuy+7d7u7vCO8U7yDvLO8470TvUO9c72jvcO/f8E7wkPDT8TTxk/Hf8jDyivLi8ury9vMA8wjzEPMY8yDzKPMw8zjzQPNS82Tze/OS86rzwvPa8/L0CvQi9Dr0UvRq9IL0mvSy9L70yvTW9OL07vT69Qb1EvUe9TD1QvVO9Vr1ZvVy9X71ivWW9aL1tfXH9dn15fXx9f32CfYV9iH2NPZG9lf2Y/Zv9nv2h/aT9p/2q/a39sP2z/bb9uf28/b/9wf3D/cX9x/3J/cv9zf3P/dH90/3V/df92f3b/d794b3kfej96v3s/e/98f32ffr9/P3+/gD+Av4Hfgl+C34Nfg9+EX4TfhV+F34+/lx+dr54vnu+gD6EfoZ+iX6Mfo9+kn6Vfph+m36efqF+pH6nfqp+rX6wfrNAAAABQBkAAADKAWwAAMABgAJAAwADwBvsgwQERESObAMELAA0LAMELAG0LAMELAJ0LAMELAN0ACwAEVYsAIvG7ECID5ZsABFWLAALxuxABA+WbIEAgAREjmyBQIAERI5sgcCABESObIIAgAREjmwCtyyDAIAERI5sg0CABESObACELAO3DAxISERIQMRAQERAQMhATUBIQMo/TwCxDb+7v66AQzkAgP+/gEC/f0FsPqkBQf9fQJ3+xECeP1eAl6IAl4AAgA3/+8CIAWwAAMADgA8sgIPEBESObACELAL0ACwAEVYsAIvG7ECID5ZsABFWLAMLxuxDBA+WbEHDbAKK1gh2Bv0WbAB0LABLzAxASMTMwE0Njc2FhQGBwYmAVbMnPr+F0s6OU5LOjdQAa0EA/q/O0wCAkpySwICRwACAKED9ALCBgAABAAJACUAsABFWLADLxuxAyI+WbAC0LACL7AH0LAHL7ADELAI0LAILzAxAQMjEzMFAyMTMwGHXIpTqgENXIpTqgVs/ogCDJT+iAIMAAIAOwAABOUFsAAbAB8AjwCwAEVYsAwvG7EMID5ZsABFWLAQLxuxECA+WbAARViwAi8bsQIQPlmwAEVYsBovG7EaED5Zsh0MAhESObAdL7EAA7AKK1gh2Bv0WbAE0LAdELAG0LAdELAL0LALL7EIA7AKK1gh2Bv0WbALELAO0LALELAS0LAIELAU0LAdELAW0LAAELAY0LAIELAe0DAxASMDIxMjNzMTIzchEzMDMxMzAzMHIwMzByMDIwMzEyMCltORqpHeHPpv6RwBBZWpldSUqZTHHORu1BzxkakJ02/TAZr+ZgGangE5nwGg/mABoP5gn/7Hnv5mAjgBOQAAAQBC/y0EUQabACsAgrIfLC0REjkAsAovsABFWLAJLxuxCSA+WbAARViwIC8bsSAWPlmwAEVYsB8vG7EfED5ZsgIfCRESObAJELAM0LIQCR8REjmwCRCxEwGwCitYIdgb9FmwAhCxGQGwCitYIdgb9FmwHxCwItCyJh8JERI5sB8QsSkBsAorWCHYG/RZMDEBNiYmJyY3NjY3NzMHFhYHIzYmJyYGBwYWFhcWBwYGBwcjNyYmNxcGFhcWNgL+CVHrSKoOC+S0KJ0plJcK7AlVV1x9DQlQ6D+4DwvtvCScJaiwCe0HZmJpkQGDTHFiN4HPqtkV2t4f8r9/jQMCcGJMbGYtgtqt1RPDxBryvwF/hwICcwAABQC1/+cFPgXIAA0AGwApADcAOwCNsic8PRESObAnELAF0LAnELAW0LAnELAr0LAnELA40ACwOC+wOi+wAEVYsAAvG7EAID5ZsABFWLAjLxuxIxA+WbAAELAH0LAHL7ERArAKK1gh2Bv0WbAAELEYArAKK1gh2Bv0WbAjELAc0LAcL7AjELEtArAKK1gh2Bv0WbAcELE0ArAKK1gh2Bv0WTAxARYWBwcGBicmJjc3NjYDBhYXFjY3NzYmJyYGBwEWFgcHBgYnJiY3NzY2AwYWFxY2Nzc2JicmBgcFJwEXAg+DkggGD7mCfpkIBw23JAc4OjxYCwkHODs9WggCvYKTCAYOuoJ8mgYFC7kiBTo3PVUMCgU6N0BYCP3xeANveAXGBKqATYmmBAKqf0qJqv6BQFcCAldGTkFYAgJdSv4CBKp+ToepBAKmhEGOrf6CRVMCAlNLT0hQAgJdSO5PBGdPAAMALf/pBKEFyAAeACgANAB0si01NhESObAtELAR0LAtELAh0ACwAEVYsAkvG7EJID5ZsABFWLAYLxuxGBA+WbAARViwHC8bsRwQPlmyEgkYERI5shUJGBESObEfAbAKK1gh2Bv0WbIjCRgREjmyLAkYERI5sAkQsTIBsAorWCHYG/RZMDETNjc3JiY3NjYXHgIHBgYHBxM2NzcCBxchJwYnJiYFFjY3AwcGBwYWEwYXFzc2NzYmIyIGOAzGcj0oBAzkrF2WUAUFaXZ51lMVyxigof7+PbDHu+wBt0R4OPMiiREMaHAKMBdjgQwGSDdIZAGBtoxLcI0/qtQEA1KRV1qdUlD+vHyQAf7wrfpfdgQC3h4BNCMBcRZgd2B4A6BFXCo+Umo5SWkAAQCQA/wBlgYAAAQAFgCwAEVYsAMvG7EDIj5ZsALQsAIvMDEBAyMTMwGBVJ1RtQV3/oUCBAAAAQBo/jEDIAZgABEAELIGEhMREjkAsAMvsAwvMDETEgA3FwADBgcGEhcHJgITNjeANQFP+CT+qmYlAQJkYjirtwgCDAJMAW0COW6Q/vj9zM6/y/7RV4VqAcABKmBWAAH/lP4vAlAGXwAPABCyCRARERI5ALAIL7AALzAxAyc2EhM3ECc3FhYSBwICAEcl1PAaBMQ5c6NPBAmz/t7+L4qlAi8Bf3wBpayGRv3+pLX+6f31/pcAAQBnAksDpQWwAA4AIACwAEVYsAQvG7EEID5ZsADQGbAALxiwCdAZsAkvGDAxASU3BRMzAyUXBRMHAwMnAX/+6E8BFy2wSwEuGP7Bl5V83IYD0VihdwFd/qhwtFj+8WIBIf7sbgAAAQA9AJIELgS2AAsAGwCwCS+wANCwCRCxBgGwCitYIdgb9FmwA9AwMQEhByEDIxMhNyETMwK9AXEn/pBL50z+jCgBckbnAyHe/k8Bsd4BlQAB/4n+uAEUAOsABwAZsgcICRESOQCwCC+xBA2wCitYIdgb9FkwMRMnNjc3MwcGCH92GyXVGij+uFCed86h9wAAAQA2AgkCWALNAAMAEgCwAi+xAQGwCitYIdgb9FkwMQEhNyECNf4BIwH/AgnEAAEAMP/yAUMBAwALACOyCAwNERI5ALAARViwCS8bsQkQPlmxAw2wCitYIdgb9FkwMTc0Njc2FhUUBgcGJjBNPDtPTD07T3U9TQICSzs6TQICSgAB/3//gwOCBbAAAwATALAAL7AARViwAi8bsQIgPlkwMRcjATNDxAM+xX0GLQAAAgBg/+cEOgXJABEAIABIshchIhESObAXELAA0ACwAEVYsAkvG7EJID5ZsABFWLAALxuxABA+WbAJELEWAbAKK1gh2Bv0WbAAELEeAbAKK1gh2Bv0WTAxBSYmNzY3ExIAFxYWBwYHBwIAEzY1JicmBgcDBhcUFxYTAd+9wgMBCScxARjevMMDAQknM/7riA0FoHqUHi4MAaTiQRQE/eRKSgEEATIBLgUE+ORLSf3+x/7NA5ByMOIHBbzN/sNnPOoHDQFuAAEA7wAAA3gFtQAGADoAsABFWLAFLxuxBSA+WbAARViwAC8bsQAQPlmyBAAFERI5sAQvsQMBsAorWCHYG/RZsgIDBRESOTAxISMTBTclMwKB7Mr+kCUCQCQEjHrXzAABAAsAAAQ/BccAGABXsgkZGhESOQCwAEVYsBAvG7EQID5ZsABFWLAALxuxABA+WbIDEAAREjmwEBCxCAGwCitYIdgb9FmyDBAAERI5shUQABESObAAELEXAbAKK1gh2Bv0WTAxISE3ATY3NiYnJgYHBz4CFxYWBwYHBwEhA8L8SRwCXakRDVpab5gQ7AqP7Yq+3Q0R5D7+WwKHsQJFpYZffwQEk38BhtZ3AwTUsszjPf50AAABACb/6AQ5BcUAKgBqsggrLBESOQCwAEVYsA8vG7EPID5ZsABFWLAbLxuxGxA+WbAB0LABL7APELEHAbAKK1gh2Bv0WbAPELAL0LABELEpAbAKK1gh2Bv0WbIVKQEREjmwGxCwINCwGxCxIwGwCitYIdgb9FkwMQEXMjY3NiYnJgYHBz4CFxYWBwYGBxYXFQYEJy4CNxcGFhcWNjc2JicnAaCBdZwLC15dXooO7QmI23/D4Q0Hhn+tCw3+2dZ7xGkE7ARnY22ZDAxzbJkDRwF+aWNxAgJyXQF1uGMBBNu4ZKc8UMYwxPQEAWe7eAFgdQMEiG5vdAMBAAIACQAABCoFsAAKAA4ASgCwAEVYsAkvG7EJID5ZsABFWLAELxuxBBA+WbIBCQQREjmwAS+xAgGwCitYIdgb9FmwBtCwARCwC9CyCAYLERI5sg0JBBESOTAxATMHIwMjEyE3ATMBIRMHA3qwIq857Tj9nhUDAv39BwFpcRgCB8P+vAFEoAPM/FcCYyIAAQBa/+cEcwWwAB0AbbIaHh8REjkAsABFWLABLxuxASA+WbAARViwDS8bsQ0QPlmwARCxAwGwCitYIdgb9FmyBwENERI5sAcvsRoBsAorWCHYG/RZsgUHGhESObANELEUAbAKK1gh2Bv0WbIRFBoREjmyHRoUERI5MDETEyEHIQM2MxYSBwYAJyYmJzMWFhcWNjc2JicmBge6vwL6If3PZ2Z4uccSEv7c17bjBuMHZVtvlw8MamlAZTAC1QLb0v6jOgL+9NXb/uoEBOK5ZnMCA6iMfJkCAi0oAAACAGP/6AQTBbgAFwAlAF6yGSYnERI5sBkQsAbQALAARViwAC8bsQAgPlmwAEVYsA8vG7EPED5ZsAAQsQIBsAorWCHYG/RZsgcADxESObAHL7EYAbAKK1gh2Bv0WbAPELEgAbAKK1gh2Bv0WTAxAQcnJgQHNhceAgcOAicmJicmNxIAIQEmBgcGFxQWFxY2NzYmA8wUDcD+5lCEqXWkTAwMjuWIrdgPCSBBAakBSP60UIwwCwFeWGyXDw1gBbjKAQLT1oAEAn/dgo7tgQME7sJrswFlAZb9SQJZUmUrgJYCA6iIf6IAAAEAhgAABJwFsAAGADMAsABFWLAFLxuxBSA+WbAARViwAS8bsQEQPlmwBRCxAwGwCitYIdgb9FmyAAMFERI5MDEBASEBITchBIX9BP79Avn9Kh8D1AUd+uME7cMAAwA7/+gERQXIABYAIgAuAG6yGi8wERI5sBoQsBLQsBoQsCfQALAARViwEy8bsRMgPlmwAEVYsAgvG7EIED5ZsCzQsCwvsRoBsAorWCHYG/RZsgIsGhESObINGiwREjmwCBCxIAGwCitYIdgb9FmwExCxJgGwCitYIdgb9FkwMQEGBxYWBwYEJyYmNzYlJiY3NiQXHgIBNiYnJgYHBhYXFjYTNiYnJgYHBhYXFjYEPBLuWVcIDf7g1cLlDRIBEUtIBg4BDMd3tVr+tQtkXmqWDAtmXWyTYAlVU1uBCwlWUVyBBDjZdzmwasDtBATftfN9NqFcvOUEA2S0/PhlgwICj21newICigL7WnYCAoBmXnICAoIAAgCO//kELwXIABgAJgBbshknKBESObAZELAV0ACwAEVYsA0vG7ENID5ZsABFWLAWLxuxFhA+WbEAAbAKK1gh2Bv0WbIFFg0REjmwBS+xGQGwCitYIdgb9FmwDRCxIQGwCitYIdgb9FkwMTcWJDcGJy4CNz4CFx4CFxYHAgAhIzcBFjY/AjYmJyYGFxYW99QBCkKImHGmUgwNj+SHda1gBwUcQP5c/rwWEwFKSoQwDQQDWFh9oA8HWsIC0dGEAgJ34IiR8oQEA3HRgWug/o7+eMoB2gJVS2FGgpkEBPioWWwA//8AK//yAdAEVAAmABL7AAAHABIAjQNR////mv64AbwEVAAnABIAeQNRAAYAEBEAAAEAMgCqA8MEVAAGABeyAAcIERI5ALAARViwBS8bsQUcPlkwMQEFBwE3AQcBMgIWKf0TIgNvLQJy4OgBdcEBdP4AAAIAYgFkBBQD1gADAAcAJwCwBy+wA9CwAy+xAAGwCitYIdgb9FmwBxCxBAGwCitYIdgb9FkwMQEhNyEDITchA/H8uiQDRW38uyMDRgMMyv2OyQABAC8AnwPZBEkABgAXsgAHCBESOQCwAEVYsAIvG7ECHD5ZMDEBJTcBBwE3Atv9zygDByL8eCwCgePl/ovB/oz6AAACAJX/8QPfBckAGAAkAGCyHiUmERI5sB4QsArQALAARViwEC8bsRAgPlmwAEVYsCIvG7EiED5ZsRwNsAorWCHYG/RZsADQsAAvsgQQABESObAQELEJAbAKK1gh2Bv0WbINECIREjmyFQAQERI5MDEBPgI3NicmJicmBgcHNiQXFhYHBgcHBgcBNDY3NhYVFAYjBiYBPwxdyx9eEghIOVJxEewRAQC+scoOD716XhT+1ks6OE5PNjhOAat9sKwkbHY0PQECY1UBstIEBM6qsaNmVo3+xTtMAgJKOT1JAkcAAAIAMv47BqQFkwA7AEcAgLIeSEkREjmwHhCwRdAAsCsvsDQvsABFWLAALxuxABA+WbAARViwBS8bsQUQPlmyAzQAERI5sgw0ABESObAML7AAELE+BLAKK1gh2Bv0WbAU0LA0ELEdArAKK1gh2Bv0WbArELEmBLAKK1gh2Bv0WbAMELFEBLAKK1gh2Bv0WTAxBSYmJwYnJiY3NhI2FxYWFwMHBhYXFjYSNzYmJyYnJgQCAgcGEhYXFjcXBiMmJAInJhIAJBcWBBIXFgIGAQYXFjY3EyYnJgYHBKZNdhSDi3J6CQef4oRVhUOGCAcoL1mJVgcEOzx98qf+2uuFBwhp25+mrRuK5cP+3ZwEBJ4BIAFvycABGpoEBIHn/WMFajh3HYEtKYKxJBUCSk6cAwK1oKEBT64CAjkw/ck8P0kCBJABE6yG1keSBAOR/t/+i76t/vSLAQJLjFYBpAE4090BwAFasQMDov7JyNP+ksQBTKIDA2tMAfERAgX75QAAAv+kAAAErgWwAAcACgBUsgQLDBESObAEELAK0ACwAEVYsAQvG7EEID5ZsABFWLACLxuxAhA+WbAARViwBi8bsQYQPlmyCAQCERI5sAgvsQABsAorWCHYG/RZsgoEAhESOTAxASEDIQEzASMBIQMDff3frv72AxLeARr4/g4BmGMBU/6tBbD6UAIfAloAAAMAJwAABLwFsAANABYAHgBsshgfIBESObAYELAN0LAYELAQ0ACwAEVYsAIvG7ECID5ZsABFWLAALxuxABA+WbAX0LAXL7KfFwFdsQ4BsAorWCHYG/RZsgcOFxESObAAELEQAbAKK1gh2Bv0WbACELEdAbAKK1gh2Bv0WTAxMxMFFhYHBgcWFgcGBCMDAwUyNjc2JiclFzI2NzYnJSf9Ab/s7Q4S8VpiBw7+2/CtTwEDdaQPDlpo/vjjepoOGdb+/wWwAQHLtNRrIKp2yOgCkf45AXxsZ3QEuwF0Y7sHAQAAAQBl/+gFDQXHAB4AULILHyAREjkAsABFWLAMLxuxDCA+WbAARViwAy8bsQMQPlmyAAwDERI5shAMAxESObAMELETAbAKK1gh2Bv0WbADELEcAbAKK1gh2Bv0WTAxAQYAJy4CJyYSEiQXFhIXIyYmJyYGDwIGFhYXBBMEqiX+sPGL0XYHBkTBARms2f0I9QV5d6PcJhQJCC1yWAEXTwHb5P7xBAN+8ZhyAYkBOJ4DBP736ZyLAwX06YVmZ7VfAwsBLQACACcAAATgBbAACwAWAEiyChcYERI5sAoQsA/QALAARViwAS8bsQEgPlmwAEVYsAAvG7EAED5ZsAEQsQwBsAorWCHYG/RZsAAQsQ4BsAorWCHYG/RZMDEzEwUyBBIHBwYCBCMTAxcyJDc2JyYmJyf8AYq2AQd2Fwsezf68wiq2ksYBBSUaBwmXhgWwAbX+wcBPyf7JrATk++YB+92YcZGkBAABACcAAAS6BbAACwBRALAARViwBi8bsQYgPlmwAEVYsAQvG7EEED5ZsgsGBBESObALL7EAAbAKK1gh2Bv0WbAEELECAbAKK1gh2Bv0WbAGELEIAbAKK1gh2Bv0WTAxASEDIQchEyEHIQMhA9P9vE4CpiP8Y/wDlyT9YUYCRQKK/kDKBbDM/m4AAQAnAAAEpwWwAAkAQgCwAEVYsAQvG7EEID5ZsABFWLACLxuxAhA+WbIJBAIREjmwCS+xAAGwCitYIdgb9FmwBBCxBgGwCitYIdgb9FkwMQEhAyMTIQchAyEDwf3Ia/f8A4Qk/XRLAjkCaf2XBbDM/k8AAQBr/+oFFgXIACEAXrIfIiMREjkAsABFWLANLxuxDSA+WbAARViwAy8bsQMQPlmwDRCwENCwDRCxEwGwCitYIdgb9FmwAxCxGwGwCitYIdgb9FmyIA0DERI5sCAvsR8BsAorWCHYG/RZMDElBgQnLgInJhISNzYXFhYXJwInJgYHBgcGFhcWNxMhNyEEkFD+3LSQ3IEJB0CldqDO2/cQ7xbjqtsoFwIGj4mvcTb+3CICF71oawIBf/ObeAF0ASFSbwQE9NwBAQEHBfnriVezzgIEWwEdwAAAAQAnAAAFhwWwAAsAVLIHDA0REjkAsABFWLAGLxuxBiA+WbAARViwCi8bsQogPlmwAEVYsAAvG7EAED5ZsABFWLAELxuxBBA+WbIJBgAREjmwCS+xAgGwCitYIdgb9FkwMSEjEyEDIxMzAyETMwSK9nD9inD3/fdqAnZp9wKH/XkFsP2iAl4AAAEANQAAAigFsAADAB0AsABFWLACLxuxAiA+WbAARViwAC8bsQAQPlkwMSEjEzMBK/b99gWwAAEAA//nBGEFsAAOADeyDA8QERI5ALAARViwAC8bsQAgPlmwAEVYsAUvG7EFED5ZsggABRESObELAbAKK1gh2Bv0WTAxATMDBgQnJiY3FwYXFjY3A2v2rh/+49HM1wr2DsBkjxUFsPwD1PgEBOrHAeUEBIZ6AAABACcAAAVxBbAADABTALAARViwBC8bsQQgPlmwAEVYsAgvG7EIID5ZsABFWLACLxuxAhA+WbAARViwCy8bsQsQPlmyAAQCERI5tGoAegACXbIGBAIREjm0ZQZ1BgJdMDEBBwMjEzMDNwEhAQEhAjPITff993WZAfYBPP14AZn+7AJzt/5EBbD9Y58B/v1v/OEAAAEAJwAAA8MFsAAFACkAsABFWLAELxuxBCA+WbAARViwAi8bsQIQPlmxAAGwCitYIdgb9FkwMSUhByETMwFBAoIk/Ij998rKBbAAAQAnAAAGzgWwAA4AbgCwAEVYsAAvG7EAID5ZsABFWLACLxuxAiA+WbAARViwBC8bsQQQPlmwAEVYsAgvG7EIED5ZsABFWLAMLxuxDBA+WbIBAAQREjm0ZQF1AQJdsgcABBESObRqB3oHAl2yCgAEERI5tGoKegoCXTAxARMBIQMjExMBIwsCIxMCXtUCVwFE/PZVgf2ost9bUfb9BbD7pgRa+lAB7QJf+7QEbf1m/i0FsAAAAQAnAAAFhgWwAAkATLIBCgsREjkAsABFWLAFLxuxBSA+WbAARViwCC8bsQggPlmwAEVYsAAvG7EAED5ZsABFWLADLxuxAxA+WbICBQAREjmyBwUAERI5MDEhIwEDIxMzARMzBInv/jm19/3vAce29gQT++0FsPvpBBcAAAIAa//nBSEFyAASACIASLIZIyQREjmwGRCwANAAsABFWLAKLxuxCiA+WbAARViwAC8bsQAQPlmwChCxGAGwCitYIdgb9FmwABCxHwGwCitYIdgb9FkwMQUuAicmEhI3NhcWABcWAgIHBhM3NiYmJyYGAgcGFhcWEjcCV47XeAgHO5dprePYAQEMBjmLZ7LaCQYyd1t+w3kKCoSEreEjFAOC9519AU4BE1eOBAT+3vd8/r/+81qcAxhqbblhAwSW/s7nt9IEBQEO9QACACcAAAUEBbAACgATAE+yChQVERI5sAoQsAzQALAARViwAy8bsQMgPlmwAEVYsAEvG7EBED5ZsgsBAxESObALL7EAAbAKK1gh2Bv0WbADELETAbAKK1gh2Bv0WTAxAQMjEwUyBAcGBCMlBTI2NzYmJyUBfF73/QH45AEEERL+yvv+7wEbhqsRDm9w/swCHf3jBbAB+c3U+cwCiHpvhwUBAAACAGT/BAUaBcgAFgAmAEiyAycoERI5sAMQsCTQALAARViwDi8bsQ4gPlmwAEVYsAUvG7EFED5ZsA4QsRwBsAorWCHYG/RZsAUQsSMBsAorWCHYG/RZMDElFwclBicmACcmEhI3NhceAhcWBwcCAzc2JiYnJgIDBhYWFxYSNwOr0K7/AFAv1f79DAY7nXOo2JDWegcECgw+rQkGM3hbxPEOBjR3WaXiKFbIivQMAQIBJPZ9AUkBHlmCBAOC+5xWVlf+bgHtam64YAMG/pf+uG+6YQMHAQDzAAACACcAAATYBbAADgAXAFyyBRgZERI5sAUQsBDQALAARViwBC8bsQQgPlmwAEVYsAIvG7ECED5Zsg8CBBESObAPL7EBAbAKK1gh2Bv0WbILAQ8REjmwAhCwDtCwBBCxFwGwCitYIdgb9FkwMQEhAyMTBTIWBwYGBxMHIQEXMjY3NiYnJQKW/upi9/0By+38EQumltcB/vr+Uu+BrQ8PbnD++AIx/c8FsAHky43PO/2mDwL8Aod0cXkEAQABACT/6gS7BccAKQBksgMqKxESOQCwAEVYsAovG7EKID5ZsABFWLAfLxuxHxA+WbIDHwoREjmwChCwDtCwChCxEgGwCitYIdgb9FmwAxCxGAGwCitYIdgb9FmwHxCwJNCwHxCxJwGwCitYIdgb9FkwMQE2JycmJjc+AhceAgcnNiYnJgYHBhcXFhYHDgInLgI3FwYWFxY2A0wWs1HivgkImfqNiNRwBPYHc3R1oQ4UvkvltgsKjvuXj+l8BfcIioF4oQF+kEYeT9iPfL1mAwNxyYEBcn4DAnJhf0kbUt2Xe7dkAgF20YUBfIYCAmoAAQCcAAAFIgWwAAcALwCwAEVYsAYvG7EGID5ZsABFWLACLxuxAhA+WbAGELEAAbAKK1gh2Bv0WbAE0DAxASEDIxMhNyEE/v5I2fba/kskBGIE5PscBOTMAAEAW//mBS8FsAASAD2yDxMUERI5ALAARViwAC8bsQAgPlmwAEVYsAkvG7EJID5ZsABFWLAELxuxBBA+WbEOAbAKK1gh2Bv0WTAxAQMGACcmAjc3EzMDBhYXFjY3EwUvpSL+teva/QsDpfalEnZ7h7QZpwWw/DPp/uwEBAEAziYDzvwxi5wEBJqQA9QAAQCbAAAFgQWwAAYAOLIABwgREjkAsABFWLABLxuxASA+WbAARViwBS8bsQUgPlmwAEVYsAMvG7EDED5ZsgABAxESOTAxAQEhASMBIQJRAhgBGP0g7/7pAQYBPwRx+lAFsAABALcAAAc6BbAADABgsgUNDhESOQCwAEVYsAEvG7EBID5ZsABFWLAILxuxCCA+WbAARViwCy8bsQsgPlmwAEVYsAMvG7EDED5ZsABFWLAGLxuxBhA+WbIAAQMREjmyBQEDERI5sgoBAxESOTAxAQEzASMDASMDMxMBMwS7AYT7/dbsZf5I7mLvMAG3zwFqBEb6UAQk+9wFsPu/BEEAAAH/wwAABUcFsAALAFMAsABFWLABLxuxASA+WbAARViwCi8bsQogPlmwAEVYsAQvG7EEED5ZsABFWLAHLxuxBxA+WbIAAQQREjmyBgEEERI5sgMABhESObIJBgAREjkwMQEBIQEBIQMBIQEBIQKjAXoBKv3bAT7+7tz+fP7VAjH+yQEQA6MCDf0j/S0CFf3rAukCxwABAKEAAAVNBbAACAAxALAARViwAS8bsQEgPlmwAEVYsAcvG7EHID5ZsABFWLAELxuxBBA+WbIAAQQREjkwMQEBIQEDIxMBIQJzAbwBHv1+W/hg/skBBQMAArD8W/31AiUDiwAAAf/lAAAE5wWwAAkARgCwAEVYsAcvG7EHID5ZsABFWLACLxuxAhA+WbEAAbAKK1gh2Bv0WbIEAAIREjmwBxCxBQGwCitYIdgb9FmyCQUHERI5MDElIQchNwEhNyEHAToC7CT74x8Djf0yJAQAHsrKsAQ0zKwAAAH/7/68ArUGjgAHACQAsAQvsAcvsQABsAorWCHYG/RZsAQQsQMBsAorWCHYG/RZMDEBIwMzByEBIQKXn/6gHv5zATkBjQXQ+am9B9IAAAEArP+DAsgFsAADABMAsAIvsABFWLAALxuxACA+WTAxEzMBI6zgATzgBbD50wAB/3r+vAJDBo4ABwAnALACL7ABL7ACELEFAbAKK1gh2Bv0WbABELEGAbAKK1gh2Bv0WTAxEyEBITczEyO0AY/+x/5wHqL+owaO+C69BlcAAAEARALZAy4FsAAGACeyAAcIERI5ALAARViwAy8bsQMgPlmwANCyAQcDERI5sAEvsAXQMDEBAyMBMxMjAhT90wGgp6O9BKT+NQLX/SkAAAH/ef9BAxYAAAADABwAsABFWLADLxuxAxA+WbEAAbAKK1gh2Bv0WTAxBSE3IQL0/IUiA3u/vwAAAQDKBNECVgYAAAMAJACwAS+yDwEBXbAD0LADL7QPAx8DAl2yAAEDERI5GbAALxgwMQEjAzMCVrXX/gTRAS8AAAIAIv/oA9wEUAAgACsAiLIKLC0REjmwChCwJtAAsABFWLAYLxuxGBw+WbAARViwAC8bsQAQPlmwAEVYsAQvG7EEED5ZsgIEGBESObIKGAAREjmwCi+wGBCxEAewCitYIdgb9FmyEwoQERI5QAkMExwTLBM8EwRdsAQQsSEBsAorWCHYG/RZsAoQsSYHsAorWCHYG/RZMDEhJjcGJyYmNzYkMxc3NicmJyYGBwc+AhcWFgcDBwYXByUWNjc3JyIGBwYWApMMAoabjbkGCAEY7JoOBgYUe0xzDe0HgNR2scYRUwgDEgH+IUuALSVxhqALCEsoPX0EArGIq8QCSicibAMCUUQCZJdUAgTNo/4FWjs4Eq4CSTrNAWVYQ00AAgAQ/+gEDwYAABEAHgBmsgQfIBESObAEELAb0ACwCS+wAEVYsA0vG7ENHD5ZsABFWLAHLxuxBxA+WbAARViwBC8bsQQQPlmyBg0HERI5sgsNBxESObANELEVAbAKK1gh2Bv0WbAEELEaAbAKK1gh2Bv0WTAxAQYCBicmJwcjATMDNhcWFhcWJzQmJyYHAxYXFjY3NgQHFInLf7VcJtkBCu5seaadsQUB7FpVj2NOLJF4mxYIAhil/vWAAwSHdgYA/dGBBATewTwvbXsCBI7+QIgFA76tVQAAAQA4/+kD7gRSABwATbIAHR4REjkAsABFWLARLxuxERw+WbAARViwCC8bsQgQPlmxAAGwCitYIdgb9FmyBBEIERI5shUIERESObARELEYAbAKK1gh2Bv0WTAxJRY2NzcOAicuAjc3PgIXFhYVIzQmJyYGBwIB6FWDEuALhdBxi8RaDwMRleyQsNLeW1aLoAYHrQJnUwFrsGIDAoz3mCOd/4oEBOG0XXYEBPTe/vMAAgA7/+cEiAYAABIAHQBjsgQeHxESObAEELAb0ACwBy+wAEVYsAQvG7EEHD5ZsABFWLAJLxuxCRA+WbAARViwDS8bsQ0QPlmyBgQJERI5sgsECRESObEWAbAKK1gh2Bv0WbAEELEbAbAKK1gh2Bv0WTAxEzYSNhcWFxMzASM3BicmJicmNxcGFhcWNxMmJyYGRBSMzn6lXWju/vXUEH6ql7UHAwbpB1taiWRRL4eIpgIepwEKgwMEdwIs+gBwiQQC5b4+O0h8kgIEiQHRfQQE+AAAAgA7/+oEAgRRABYAHwCGshEgIRESObARELAX0ACwAEVYsAkvG7EJHD5ZsABFWLAALxuxABA+WbIaAAkREjmwGi+0vxrPGgJdtF8abxoCcbQfGi8aAnGyjxoBXbTvGv8aAnGxDQewCitYIdgb9FmwABCxEQGwCitYIdgb9FmyEwkAERI5sAkQsRcBsAorWCHYG/RZMDEFLgI3NzYSNhcWEgcHIQYWFxY3FwYGAyYDBTc2JyYmAfqNz2MMAxKd6onLyxkO/VcJemuZgXhE3h+8XgHBBAcGC1oUA4jskSmlAQeIAwT+2uxogZ4CBYp+YWsDogb+8AEVLixHUgAAAQBfAAADXgYaABUAZbIVFhcREjkAsABFWLAILxuxCCI+WbAARViwAy8bsQMcPlmwAEVYsBEvG7ERHD5ZsABFWLAALxuxABA+WbADELEBAbAKK1gh2Bv0WbAIELENAbAKK1gh2Bv0WbABELAT0LAU0DAxMxMjNzM3NjYXFhcHJiMmBgcHMwcjA2OdoSCgEBrbrT1QGiwtVWwPD9Yg1Z0DhrR0qMQCAhK+CgFeU2a0/HoAAAL/9/5PBEIEUQAcACoAhrIEKywREjmwBBCwI9AAsABFWLAILxuxCBw+WbAARViwBC8bsQQcPlmwAEVYsAwvG7EMEj5ZsABFWLAYLxuxGBA+WbIGCBgREjmwDBCxEgGwCitYIdgb9FmyEBIYERI5shYIGBESObAYELEiAbAKK1gh2Bv0WbAEELEnAbAKK1gh2Bv0WTAxEzYSNhcWFzcXAwYEJyYmJzcWFxY2NzcGJyYmJyY3BhcWFhcWNxMmJyYGB0YTidCGslsl2LMe/tfVcsw+fl+ZdKccEX2fmLcJA/MGAgJcVYdlVTSFeKQZAh6iAQaLAgR/bwH75NT7BgJkUo+DBASHfUx5BALivzw+MztqfAMFggHedwQDwK0AAQANAAAD+QYAABIASrIBExQREjkAsBEvsABFWLACLxuxAhw+WbAARViwBi8bsQYQPlmwAEVYsA8vG7EPED5ZsgACBhESObACELEMAbAKK1gh2Bv0WTAxATYXFhYHAyMTNicmJyYHAyMBMwGXh6yalRN07XYFAw2DhGiH7QEK7gPDjgQC1r39SAK7KyV6AwKE/PoGAAAAAgAfAAACCQXYAAMADwA/sgQQERESObAEELAA0ACwAEVYsAIvG7ECHD5ZsABFWLAALxuxABA+WbACELAN0LANL7EHDbAKK1gh2Bv0WTAxISMTMwM0Njc2FhUUBgcGJgEM7bzty0g9Ok1LOjlOBDoBFTdOAgJLNjlKAgJJAAL/DP5GAf4F2AAMABgAS7IBGRoREjmwARCwDdAAsABFWLAALxuxABw+WbAARViwBC8bsQQSPlmxCQGwCitYIdgb9FmwABCwFtCwFi+xEA2wCitYIdgb9FkwMQEDBgYnJic3FjMyNxMTNDY3NhYVFAYHIiYBw8cWvJdARxQuJn8ayR1IPDpNSzo8SgQ6+2eoswICEcALlQSVARU6SwICSTg5SgJHAAABABEAAARKBgAADABTALAARViwBC8bsQQiPlmwAEVYsAgvG7EIHD5ZsABFWLACLxuxAhA+WbAARViwCy8bsQsQPlmyAAgCERI5tGoAegACXbIGCAIREjm0ZQZ1BgJdMDEBBwMjATMDNwEhAQEhAb+GO+0BCu2YUwFYAS/+IAE8/v8Bznf+qQYA/JhWAUz+Mv2UAAEAHwAAAhcGAAADAB0AsABFWLACLxuxAiI+WbAARViwAC8bsQAQPlkwMSEjATMBDO0BC+0GAAAAAQAQAAAGaARSACEAeLIWIiMREjkAsABFWLADLxuxAxw+WbAARViwCC8bsQgcPlmwAEVYsAAvG7EAHD5ZsABFWLAMLxuxDBA+WbAARViwFi8bsRYQPlmwAEVYsB8vG7EfED5ZsgEIDBESObIGCAwREjmwCBCxEgGwCitYIdgb9FmwHNAwMQEHNhcWFhc2FxYWBwMjEzYnJicmBwcDIxM2JyYnJgcDIxMBqRWGumaHGJbCnpkTde12BQQQhJNVA3zudgUEEISFWYntuwQ7c4oEAlpKqgQE0bz9QwK/LCV1AwSlFv0vArwrJXkDAnn87wQ6AAABAA0AAAP6BFIAEgBUsgITFBESOQCwAEVYsAMvG7EDHD5ZsABFWLAALxuxABw+WbAARViwBy8bsQcQPlmwAEVYsBAvG7EQED5ZsgEDBxESObADELENAbAKK1gh2Bv0WTAxAQc2FxYWBwMjEzYnJicmBwMjEwGnGIu2mJITde12BQQNgYdmh+27BDt/lgQD0739RQK+KyV3AwKH/P0EOgAAAgA5/+gEJwRSABAAIABFshshIhESObAbELAE0ACwAEVYsAQvG7EEHD5ZsABFWLAMLxuxDBA+WbEUAbAKK1gh2Bv0WbAEELEbAbAKK1gh2Bv0WTAxEzYSNhceAgcGAgYnLgI3FxYWFxY2Nzc0JicmBwYHBkkRmfCSi8pdDhCb8ZOKyV4N7AVlWnqlFQZmYZhYNQ4IAiGfAQSOBAKQ+pms/viNBAKP+ZZ0aX8DA8KoYoCSBASZXXlUAAL/x/5gBA0EUgASAB4AabIEHyAREjmwBBCwHdAAsABFWLANLxuxDRw+WbAARViwCi8bsQocPlmwAEVYsAcvG7EHEj5ZsABFWLAELxuxBBA+WbILDQcREjmwDRCxFwGwCitYIdgb9FmwBBCxHAGwCitYIdgb9FkwMQEGAgYnJicDIwE3BzYXHgIXFgc3NiYnJgcDFhcWNgQFFIXNf6lhYe4BBNkSfKtnmFEDAfIFA1tbhmJULYp2oQIZov74hwMEdP39BdoBcIcEAWfEeD0/SYGOAgR//h15BAO+AAACADv+YAQ4BFIAEgAgAG2yBCEiERI5sAQQsBjQALAARViwCC8bsQgcPlmwAEVYsAQvG7EEHD5ZsABFWLAJLxuxCRI+WbAARViwDS8bsQ0QPlmyBggNERI5sgsIDRESObEXAbAKK1gh2Bv0WbAEELEdAbAKK1gh2Bv0WTAxEzYSNhcWFzcXASMTBicmJicmNzMHBhYXFjY3EyYnJgYHRBSOzn+sXCfW/vztYnmcm7QHAwbuBQNbWEtvLVg0gnKfHAIfqwEJfwMEfW0B+iYB/XUEAuO+PzxIh4sCA0U4Ae5yBAOypAABABAAAALvBFMADQBHsgkODxESOQCwAEVYsAgvG7EIHD5ZsABFWLALLxuxCxw+WbAARViwBS8bsQUQPlmwCxCxAgGwCitYIdgb9FmyCQsFERI5MDEBJiMmBwMjEzcHNhcyFwLULi+cXILtu+EYb5EhOgNcCgSF/RsEOgF7kwMPAAEAHP/pA8QEUAAkAHeyIyUmERI5ALAARViwCC8bsQgcPlmwAEVYsBsvG7EbED5ZsgMbCBESObILCBsREjmyHAsBXbILCwFdsAgQsQ8BsAorWCHYG/RZsAMQsRMBsAorWCHYG/RZsh4IGxESObQEHhQeAl2wGxCxIgGwCitYIdgb9FkwMQE2JCcmNzY2FxYWByc2JiciBgcGBBcWBw4CJyYmNxcWFhcyNgKXEf7dNc4HBf+yrNkC6wJWS09xCQ4BHETGBwV90nax6QLlAmRXWHUBLGNNF1i0kr8CAr6aAUtVAk4/W0ceV7lnmVEDAsqeAVdaAUkAAAEAO//tAq4FQQAWAF6yFhcYERI5ALAARViwAS8bsQEcPlmwAEVYsBQvG7EUHD5ZsABFWLAOLxuxDhA+WbABELAA0LAAL7ABELEDAbAKK1gh2Bv0WbAOELEJAbAKK1gh2Bv0WbADELAS0DAxAQMzByMDBhcWFzI3BwYjJiY3EyM3MxMCIy65H7pmAwIGSiUvEEpLfHsNZa0grC4FQf75tP2iGRRBAwm+FQKliAJqtAEHAAABAEr/6AQxBDoAEwBRsgEUFRESOQCwAEVYsAcvG7EHHD5ZsABFWLAQLxuxEBw+WbAARViwEi8bsRIQPlmwAEVYsAIvG7ECED5ZsgAQEhESObENAbAKK1gh2Bv0WTAxJQYnLgI3EzMDBhcWFxY3EzMDIwKte7lpizsMde12BAMKc51hiO273muDBAJks3kCvP1BJSN8BQaEAwr7xgABAGQAAAQNBDoABgA4sgAHCBESOQCwAEVYsAEvG7EBHD5ZsABFWLAFLxuxBRw+WbAARViwAy8bsQMQPlmyAAUDERI5MDEBATMBIwMzAboBVv39687G7gE3AwP7xgQ6AAABAHcAAAX4BDoADABgsgUNDhESOQCwAEVYsAEvG7EBHD5ZsABFWLAILxuxCBw+WbAARViwCy8bsQscPlmwAEVYsAMvG7EDED5ZsABFWLAGLxuxBhA+WbIACwMREjmyBQsDERI5sgoLAxESOTAxAQEzASMDASMDMxMBMwPhASnu/ibDX/6ixGPgKQFWswFRAun7xgLk/RwEOv0iAt4AAAH/uQAABBMEOgALAFMAsABFWLABLxuxARw+WbAARViwCi8bsQocPlmwAEVYsAQvG7EEED5ZsABFWLAHLxuxBxA+WbIACgQREjmyBgoEERI5sgMABhESObIJBgAREjkwMQETIQETIwMBIQEDMwH//wEV/mLx+Jf+9v7sAavp+ALYAWL94P3mAXH+jwIwAgoAAAH/tf5FBBIEOgAPAESyABARERI5ALAARViwDy8bsQ8cPlmwAEVYsAEvG7EBHD5ZsABFWLAFLxuxBRI+WbIABQ8REjmxCQGwCitYIdgb9FkwMQEBIQECJyYnNxcWNjc3AzMBuAFUAQb9f4bbNkUUK1ZwJjm19gFeAtz7C/8AAwISvAQDR0twBCcAAAH/5wAAA+QEOgAJAEYAsABFWLAHLxuxBxw+WbAARViwAi8bsQIQPlmxAAGwCitYIdgb9FmyBAACERI5sAcQsQUBsAorWCHYG/RZsgkFBxESOTAxJSEHITcBITchBwE4AiYi/KseAoj9/SMDNx3CwqsCy8SlAAABADD+mQMFBkAAGwA3sgwcHRESOQCwDi+wAEVYsAAvG7EAGD5ZsgkOABESObAJL7EIB7AKK1gh2Bv0WbIUCAkREjkwMQEmJjc3NicmJzc2NzcSJRcGAwcGBxYWDwIGFwHNnpwTHAUEDYYRxx8fOQFjI8EjHSG5STYJHgMDg/6ZM/CuzC0neguyCt3gAVBoj0b++trFYDeiWOZHqjoAAAEAIP7yAdIFsAADABMAsAAvsABFWLACLxuxAiA+WTAxEyMBM8SkAQ6k/vIGvgAB/5n+lQJvBjsAHAA3shodHhESOQCwDi+wAEVYsBwvG7EcGD5ZshYOHBESObAWL7EXB7AKK1gh2Bv0WbIFFxYREjkwMQc2Nzc2NyYnJj8CNCc3FhYHBwYXFhcHBgcHAgVnuCkiI75wDgUFHgSBN6OQEhwFBA2HEsgeHzn+ndtA+PTDW0qQKy3mSKo5iTbxqMwuJnwLsgrb3/6sZgABAFsBfgTKAzQAFgA+sgUXGBESOQCwDi+wANCyAxcOERI5sAMvsA4QsQgBsAorWCHYG/RZsAMQsArQsAMQsRMBsAorWCHYG/RZMDEBBgYnLgMjJgcjNjYXHgMzMjY3BMoMxJRRfnRDIYciuw7HkVKCcEQfRF0QAxSu6AQCSnQkA8Cv3AQCTHIkaVwAAAL/5v6UAc4EUAADAA4AP7ILDxAREjmwCxCwAtAAsABFWLAMLxuxDBw+WbAARViwAi8bsQIYPlmwDBCxBw2wCitYIdgb9FmwAdCwAS8wMRMzAyMBFAYGJjU0Njc2Fq/MmvsB6Ep2TEo7Ok0Clvv+BTs5TQRKODlMAgJLAAABAEz/CwQGBSYAIQBZshIiIxESOQCwAEVYsBUvG7EVHD5ZsABFWLAHLxuxBxA+WbEAAbAKK1gh2Bv0WbIEBxUREjmwBxCwCtCwFRCwEtCyGRUHERI5sBUQsRwBsAorWCHYG/RZMDElFjY3NwYGBwcjNy4CNzc2Ejc3MwcWFgcjNCYnJgIVFBYB9liAFN8O1qAvxDBriToOAhn2wS7DLoSTAt1cU4+pXK0CaFIBjccd6uwbk9+EFOUBIiLh4yHSm2FxBAb+9vBqfQAAAf/2AAAEpQXHACAAbbIcISIREjkAsABFWLATLxuxEyA+WbAARViwBS8bsQUQPlmyHhMFERI5sB4vsQABsAorWCHYG/RZsAUQsQMBsAorWCHYG/RZsAjQsAAQsAvQsB4QsA3QsBMQsBbQsBMQsRoBsAorWCHYG/RZMDEBBwYHJQchNxc2NzcjNzM3PgIXFhYHJzYmJyYGBwchBwHuFhFZAqgk/AQkRWQcGJ0jlx8Qi9l/tMsI7wVSU1p/Dh0BLiMCVq6CXwPKyQIksrnH+3/HaQQE2bYBX2cEAoZw6scAAgAI/+UFfwTxABwALABBsiItLhESObAiELAQ0ACwAEVYsAIvG7ECED5ZsBHQsBEvsAIQsSEHsAorWCHYG/RZsBEQsSkHsAorWCHYG/RZMDElBicmJwcnNyYnJhI3JzcXNhcWFzcXBxYHBgcXBwEGFhYXFjY2NzYmJicmBgYD1La8w4eYeJobChNYZnOXbq+yuYiqeak+FBqDb5j8+A9Emmlx0Y8QD0SaaXLTjGmBBAR6hJuAVVaTARx1m4WPdAQCcpScjrmnyZ6VhgJybsl5BAR52Xdux3gEBHrUAAEAMgAABLAFsAAWAHGyCxcYERI5ALAARViwAS8bsQEgPlmwAEVYsAsvG7ELED5ZsgALARESObIHAQsREjmwBy+wA9CwAy+xBQOwCitYIdgb9FmwBxCxCQKwCitYIdgb9FmwDdCwBxCwD9CwBRCwEdCwAxCwE9CwARCwFdAwMQEBIQEzByEHIQchAyMTITchNyE3MwMzAjYBZwET/jndG/7bGQEnG/7bNfY0/tUbASoZ/tQb99H+A1ECX/02mIqX/tMBLZeKmALKAAAC/+z+8gH4BbAAAwAHABgAsAAvsABFWLAGLxuxBiA+WbIFAQMrMDEDEzMDEyMTMxSL34qo4ITg/vIDG/zlA8gC9gAC/9z+IwSxBcYALgA5AISyJzo7ERI5sCcQsDTQALAIL7AARViwHy8bsR8gPlmyAggfERI5sAgQsAzQsAgQsQ8BsAorWCHYG/RZshUIHxESObIaHwgREjmwHxCwI9CwHxCxJgGwCitYIdgb9FmyLAgfERI5sBUQsTMBsAorWCHYG/RZsCwQsTkBsAorWCHYG/RZMDEBBgcWBw4CJyYmNzMGFhcyNjc2LwIkNzY3Jjc2JBcWFgcnNiYnJgcGBwYEFxYlBgcGHwI2NzYnBFIOyGENCY/wkeD7BfAGfnh4nQ0VuZJZ/usVDsZgDQ4BKuPW6wnsBnRpck5TDhYBfFTl/W55FBa2wyiBFBbCAc+1aWioeaxZAwLixWt5AmJTeEEwI3f1uGdtpLDQAgTkxgFsewICLjFahnErdCA3dog9QA87coFEAAACANEE3gODBc0ACgAWAB4AsAYvsQAFsAorWCHYG/RZsA7QsAYQsBTQsBQvMDEBMhYVFAYHIiY0NgU0NjcyFhUUBgciJgFMNkZGNThERAF4RzM4REY1OEMFzUMxM0UCRGBIeTVDAkQwM0UCQwADAF7/6AXpBccAGwApADoAmbIuOzwREjmwLhCwEtCwLhCwJ9AAsABFWLAvLxuxLyA+WbAARViwNy8bsTcQPlmyAzcvERI5sAMvtA8DHwMCXbIKLzcREjmwCi+0AAoQCgJdsgADChESObIOCgMREjmxEQKwCitYIdgb9FmwAxCxGQKwCitYIdgb9FmwNxCxHwiwCitYIdgb9FmwLxCxJgiwCitYIdgb9FkwMQEGBicmJjc3NjYXFhYHJzYmJyYGBhUXFhYXFjcFFgAXFiQSJyYCJyYEAgc2EjYkFxYEEgcGAgQnJiQCBEMMuZmSpA4KE9CelZoEmAVIUV17HQIFS0KnH/09EwEBvLgBSbcSE/zAuf63uWIRieABDZCyAR6PFRbm/qW/tv7mkAJUlqgEBNinZbzcAgSpjwFaWQICjvgbLEtYAwe5GMz++wIE2wF3wcoBAQUE2v6JKJYBF9lvAwLF/qbEyf6ayAQExAFcAAACAL4CswNQBccAHQAnAG+yEigpERI5sBIQsB7QALAARViwFi8bsRYgPlmyBCgWERI5sAQvsADQsAAvsgoEFhESObAKL7AWELEQArAKK1gh2Bv0WbAKELASsAorWNgb3FmwBBCxHgKwCitYIdgb9FmwChCwIrAKK1jYG9xZMDEBJjcGIyImNzY2Mxc3NicmJyYHJzY2FxYWBwMHBhclMjc3IwYGBwYWAm4FAl1tankEAruoaAsEAQdMdxusC7GCeowKNgQBCf61RVobU1JmCAcxAr8oHlJ7YXN9ATUZFksDBGcOb30CApZ9/qU6LS+CPooDPjUmLP//AEkAigOtA6kAJgGS7AAABwGSAUgAAAABAIABdgPKAyUABQAbALAEL7AB0LABL7AEELECAbAKK1gh2Bv0WTAxASMTITchA3/ELv2XHwMrAXYBBKsAAAQAXv/oBekFyAAPAB8AOABBAKOyOkJDERI5sDoQsAPQsDoQsB3QsDoQsDjQALAARViwBC8bsQQgPlmwAEVYsAwvG7EMED5ZsRQIsAorWCHYG/RZsAQQsRwIsAorWCHYG/RZsiEMBBESObAhL7IkBAwREjmwJC+0ACQQJAJdsiAhJBESObAgL7IgIAFdsTkIsAorWCHYG/RZsikgORESObAhELAx0LAkELFACLAKK1gh2Bv0WTAxEzYSJBcWBBIHBgIEJyYkAjceAhcWJBI3NgImJyYEAgUDIxMFFhYHBgcWFxYGFxcHIyY3Njc2JicnFzY2NzYmJydzFt4BXsWyAR6PFRbm/qW/tv7mkIoMfsl+nAEnyRcVaeCYuf63uAG4NZSFAQSPlAUHiUkHAg0BBAGVBQIBDAYsQpCBSmUKCztZigLSxgFhzwQCxf6mxMn+msgEBMQBXCuD13YDBKQBLaufAR6mBATa/oxw/q8DUgEFhnF0TC5kH3kcPhIlJCFfP0QEiAECQzY7PQMBAAEBAQUMA7cFqgADABmyAgQFERI5ALACL7EAA7AKK1gh2Bv0WTAxASE3IQOe/WMaApwFDJ4AAAIA5AOsAuQFxwALABcAMQCwAEVYsAMvG7EDID5ZsA/QsA8vsQkCsAorWCHYG/RZsAMQsRUCsAorWCHYG/RZMDETNjYXFhYHBgYnJiY3BhYzMjY3NiYjIgbmAqRvY4YCBKBsZoiKBjYxN1AGBjUvNlQEr2+pAgKZaXKjAgKWayxJTzQxSVQAAgAbAAEEBQT8AAsADwBIALAJL7AARViwDS8bsQ0QPlmwCRCwANCwCRCxBgGwCitYIdgb9FmwA9CwDRCxDgGwCitYIdgb9FmyBQ4GERI5tAsFGwUCXTAxASEHIQMjEyE3IRMzEyE3IQK4AU0g/rQ90z3+pSABWTzTYfzHHwM5A4PH/nwBhMcBefsFxAABAFYCmwLxBb8AFwBbsggYGRESOQCwAEVYsA8vG7EPID5ZsABFWLAALxuxABQ+WbEWArAKK1gh2Bv0WbICABYREjmyAw8AERI5sA8QsQgCsAorWCHYG/RZsgwPABESObITDwAREjkwMQEhNwE2NzYmJyIGBwc2NhcWFgcGDwIFAqn9rRgBVmEMByspOkMMtgqvgn+SBQWWT50BXwKbhwEZU0MpLwFHNAF5mAICg2h+dzxuAgABAGcCjgL4Bb8AJABysgklJhESOQCwAEVYsA0vG7ENID5ZsABFWLAYLxuxGBQ+WbIBGA0REjl8sAEvGLANELEHArAKK1gh2Bv0WbIKAQcREjmwARCxIwKwCitYIdgb9FmyEyMBERI5sBgQsR4CsAorWCHYG/RZshwjHhESOTAxATM2Njc2JycmBwc2NhcWFgcGBgcWBwYGJyYmNRcWFzI2NzYnIwFZUz1NBwlKF10cugmmfYGZBQNJUnYEA7yLfZmxBGo2UwcNeFwEbQI4LkMNAgJMAWl6AgN3YjtXJimBb4ICAoNtAVkCOC9ZBQAAAQDIBNEC0gYAAAMAIwCwAi+yDwIBXbAA0LAAL7QPAB8AAl2wAhCwA9AZsAMvGDAxASEBIwG1AR3+xM4GAP7RAAH/3f5gBFQEOgATAFeyDRQVERI5ALAARViwAC8bsQAcPlmwAEVYsAgvG7EIHD5ZsABFWLARLxuxERI+WbAARViwCi8bsQoQPlmwAEVYsA4vG7EOED5ZsQUBsAorWCHYG/RZMDEBAwYXFhcWNxMzAyM3BiciJwMjAQHNZggCBYWYWortu9cPaIxsUlbsAQQEOv2SVSidAwR8AxP7xlZuAjn+PQXaAAABAH0AAAPcBbEACgArsgILDBESOQCwAEVYsAgvG7EIID5ZsABFWLAALxuxABA+WbIBAAgREjkwMSETJyYmNzYAMwUDAhJaONPkFBMBK+EBLP0CCAED/8nTAQoB+lAAAQCeAkIBsQNVAAsAGbIDDA0REjkAsAMvsQkNsAorWCHYG/RZMDETNDY3NhYVFAYHBiaeTTs9Tk48O04Cxj1OAgJPODtNAgJKAAAB/9P+PQEvAAQADgAqsgIPEBESOQCwAC+wBy+xCAKwCitYIdgb9FmyDQgAERI5sgEADRESOTAxNwcWFgcGBgc3Njc2Jyc3xRM+PwECsqcCiRAJUjgtBDsOVT9tdwaNBlo8DQaJAAABAOACmwKABa4ABgA6sgEHCBESOQCwAEVYsAUvG7EFID5ZsABFWLAALxuxABQ+WbIEBQAREjmwBBCxAwKwCitYIdgb9FkwMQEjEwc3JTMB/rVjzBsBbhcCmwI2L5lzAAACAL4CrQN9BcgADgAcAEKyER0eERI5sBEQsA7QALAARViwAC8bsQAgPlmyBx0AERI5sAcvsRICsAorWCHYG/RZsAAQsRkCsAorWCHYG/RZMDEBFhYHBwYGJyYmNzc+AgMGFhcWNjc3NiYnJgYHAkqQowsGD9KZjacLBgpnpnEIRUZPbAwICEVGUGwLBcUEx5lCpM4EBMSbQm6pW/5JYWwCAnVnRmRpAgJ2ZAD//wACAIoDdQOpACYBkwkAAAcBkwFzAAD//wC5AAAFKgWrACcBxgBMApgAJwGUARQACAEHAiACsAAAABAAsABFWLAFLxuxBSA+WTAx//8AsQAABYAFrgAnAZQA6gAIACcBxgBEApsBBwHFAwIAAAAQALAARViwCS8bsQkgPlkwMf//AJYAAAWfBb8AJwGUAZ0ACAAnAiADJQAAAQcCHwCiApsAEACwAEVYsCAvG7EgID5ZMDEAAv/S/noDIwRRABgAJABjsiElJhESObAhELAC0ACwAEVYsCIvG7EiHD5ZsABFWLAQLxuxEBg+WbAiELEcDbAKK1gh2Bv0WbAA0LAAL7IEEAAREjmwEBCxCQGwCitYIdgb9FmyDBAAERI5shUAEBESOTAxAQYGBwcGBwYWFxY2NzMGBCcmJjc2Nzc2NwEUBgcGJjU0Njc2FgJrC1dfUngOC0pOU3MR7RH+/Ly3yQ0Pw21fFAEsSjo7TEo7OkwClnSrV0ptb1JgAgJlV7PTBATMqbOrXlaMATs7SwICSjg5TAICSgAAAv+HAAAHeAWwAA8AEgB7ALAARViwBi8bsQYgPlmwAEVYsAAvG7EAED5ZsABFWLAELxuxBBA+WbIRBgAREjmwES+xAgGwCitYIdgb9FmwBhCxCAGwCitYIdgb9FmyCwYAERI5sAsvsQwBsAorWCHYG/RZsAAQsQ4BsAorWCHYG/RZshIGABESOTAxISETIQMhASEHIQMhByEDIQEhEwa3/Jks/iHu/tgEJgPLI/2ONwIVI/30PAKE+1gBZlUBVP6sBbDF/mjF/jYBZwJ6AAABACMA1gQUBIYACwA4ALADL7IJDAMREjmwCS+yCgkDERI5sgQDCRESObIBCgQREjmwAxCwBdCyBwQKERI5sAkQsAvQMDETAQM3EwEXARMHAwEjAWv7nvoBan/+lfue+/6XAXcBQQFDi/6/AUGh/r/+vYsBQP7AAAADABX/oQWYBe0AFwAhACsAV7IeLC0REjmwHhCwC9CwHhCwJ9AAsABFWLAMLxuxDCA+WbAARViwAC8bsQAQPlmxJwGwCitYIdgb9FmwJdCwGtCwDBCxHQGwCitYIdgb9FmwG9CwJNAwMQUmJwcnNyY3NxISJBcWFzczBxYXFgICBAEGFwEmJyYCBwYBNicBFhcWEjc3Alece3a1wmwCAxPBATW+voBws8Q4DhFKyf7k/mEDFAJ9PoGm4ikaAtAFBv2TP2Cw4yQRFQRJlwHwsOJPAQwBfsoCBGOP9HmAqv5l/sibAiJVUwM/TgUF/wDplQEQRkf81jICBQEX+nkAAAIAJwAABIIFsAAMABUAWbIPFhcREjmwDxCwCNAAsABFWLAALxuxACA+WbAARViwCi8bsQoQPlmyAgAKERI5sAIvsg8AChESObAPL7EIAbAKK1gh2Bv0WbACELEVAbAKK1gh2Bv0WTAxAQMXFhYHBgQjJwMjExMDFzY2NzYmJwIRMcve+Q8Q/s3r/DXt/ZtV4YCsDw5wagWw/ugBAerCy/QB/tQFsP4l/hoCAolxa3wEAAABABv/5wRMBhoALQBasiEuLxESOQCwAEVYsAUvG7EFIj5ZsABFWLAALxuxABA+WbAARViwFS8bsRUQPlmyDgUVERI5sRoBsAorWCHYG/RZsiAVBRESObAFELEqAbAKK1gh2Bv0WTAxISMTNiQXFhYHDgMHBh4CBwYGJyYnNxYzMjY3NiYnJjc+Azc2JicmBgcBCO29HAEAyKe+DQQkYBwHCC6INQIJ+L2rcURnbFh2CwgyRn4JBDI8NAcJRUZadRQEUdL3BAS9nDFXmkImMWaZbjitxQQCQcFCWUk0ZkuGbzldWVw3TFwEA4OHAAMAD//oBnAEUgArADUAPgD+sgI/QBESObACELAv0LACELA50ACwAEVYsB0vG7EdHD5ZsABFWLAZLxuxGRw+WbAARViwAC8bsQAQPlmwAEVYsAUvG7EFED5ZsgMdABESObILBRkREjmwCy+wGRCxEQGwCitYIdgb9FmyFAsRERI5QAkMFBwULBQ8FARdshsdABESObI6HQAREjmwOi+0HzovOgJxso86AV20XzpvOgJxtL86zzoCXbTvOv86AnGxIQewCitYIdgb9FmwABCxJQGwCitYIdgb9FmyKB0AERI5sAUQsSwHsAorWCHYG/RZsAsQsTAHsAorWCHYG/RZsB0QsTYBsAorWCHYG/RZMDEFIiYnBicmJjc2JDMXNzYnJicmBgcnPgIXFhc2FxYSBwchBhYXFjY3FwYGJTI3NycGBgcGFgEmBgchNzYnJgRqc7w1rP2atAgKAQXmvw0GBBF3V3cN7Qd723vXWpu5wscaFf1jDndzVZdKOkHT/LZyiiipa5EMCU4DjWCSLgG2BgcEDhNTTKQEAq+TobICSiYidQMCVEkTYplTAgWAiAQG/vLWjYidAgI1J6g5Prhm0gEDXk8/SALnA4eHIS0qjQAAAgBG/+gESgYsAB4AKgBhshArLBESObAQELAo0ACwAEVYsBovG7EaIj5ZsABFWLAILxuxCBA+WbIQGggREjmwEC+wGhCxGQGwCitYIdgb9FmwEBCxIQewCitYIdgb9FmwCBCxJwGwCitYIdgb9FkwMQEWEgcHBgIGJy4CNz4CFxYXJicHJzcmJzcWFzcXASYnJgYHBhYXFjY3A6VbQRcMF6jsiX/FYAwNiOCFimsEYOA/uFulW96UyT7++DaTf6sQDmlidqMZBRSb/ryzVqf+7IkDBIDcgZDwhgQEWZmKiHlsSTDCNoN6ef05YQUCtpN4pQMF0K0AAwA9AJAEOgTPAAMADwAbAFeyGBwdERI5sBgQsADQsBgQsAbQALADL7EAAbAKK1gh2Bv0WbADELANsAorWNgb3FmxBw2wCitYIdgb9FmwABCwE7AKK1jYG9xZsRkNsAorWCHYG/RZMDEBITchATQ2NzYWFRQGBwYmAzQ2NzYWFRQGBwYmBBT8KSUD2P3CTjo9Tks+O0+OTD05UUw9OVECRtQBKT1LAgJMODlOAgJI/Qo5UAICSTw7SwICSAADACr/dwQzBLsAGwAkAC4AV7IrLzAREjmwKxCwEdCwKxCwItAAsABFWLAFLxuxBRw+WbAARViwEi8bsRIQPlmxKgGwCitYIdgb9FmwKNCwHtCwBRCxIQGwCitYIdgb9FmwH9CwJ9AwMRM2Njc2FxYXNxcHFhcWBwYCBicmJwcnNyYnJjcXBhcBJicmBgYlNicBFhcWNjc2RA9eTpzfXl9hm5JwBwIIFJv0lFZbZZuSdggDB+EBFAGUJjVkl1ACEAES/nAoKnmrHgwCIHbTTp0EAiOQAdKEwzpTn/7+iwICH5QB0YLHPTx8Pz0CZxMCAYHxgzw8/aEOAgO+r1QAAAL/zf5gBBQGAAARAB0AWLIEHh8REjmwBBCwHNAAsAkvsABFWLANLxuxDRw+WbAARViwBy8bsQcSPlmwAEVYsAQvG7EEED5ZsA0QsRYBsAorWCHYG/RZsAQQsRsBsAorWCHYG/RZMDEBBgIGJyYnAyMBMwM2FxYWFxYHNzYmJyYHAxYXFjYEDBSIzX2oYmHuAVPtanqjnbEFAfMFA1pdhWJVL4l2oQIYpP73hAMEdf39B6D91nwEBN7BPEFKf40EBH/+HXkEA74AAgA7/+gFGQYAABoAJwCCALAXL7AARViwEC8bsRAcPlmwAEVYsAYvG7EGED5ZsABFWLADLxuxAxA+WbIPFwFdsi8XAV2yFRcQERI5sBUvsRQHsAorWCHYG/RZsAHQsgQGEBESObISEAYREjmwFRCwGdCwBhCxIAGwCitYIdgb9FmwEBCxJQGwCitYIdgb9FkwMQEjAyM3BicuAicmNzYSNhcWFzcjNzM3MwczAQYXFBYXFjcTJicmBgT7qdXUEH+pY5dVBAMIFI3PfaReM/Ue8xrtGar8FQYBWlSJZVEviHafBMj7OHCIBAFpxng4WKIBCoIDBHbzqo6O/JczO2mAAgSKAdF8BAO6AAACAC4AAAXbBbAAEwAXAG0AsABFWLAPLxuxDyA+WbAARViwCC8bsQgQPlmyFAgPERI5sBQvshAUDxESObAQL7AA0LAQELEXB7AKK1gh2Bv0WbAD0LAIELAF0LAUELEHAbAKK1gh2Bv0WbAXELAK0LAQELAN0LAPELAS0DAxATMHIwMjEyEDIxMjNzMTMwMhEzMBITchBV98HXuz9nD9inD2s3gceC33LgJ2Lfb8KwJ2If2KBK6i+/QCh/15BAyiAQL+/gEC/aK6AAEAIgAAAcsEOgADAB0AsABFWLACLxuxAhw+WbAARViwAC8bsQAQPlkwMSEjEzMBD+287QQ6AAEAIQAABI0EOgAMAGAAsABFWLAELxuxBBw+WbAARViwCC8bsQgcPlmwAEVYsAIvG7ECED5ZsABFWLALLxuxCxA+WbIGAgQREjmwBi+0HwYvBgJxso8GAV2xAQGwCitYIdgb9FmyCgEGERI5MDEBIwMjEzMDMwEhAQEhActzS+y87EtIAZEBNv4HAUX+5QGs/lQEOv5QAbD95/3fAAEAIQAAA9IFsAANAF0AsABFWLAMLxuxDCA+WbAARViwBi8bsQYQPlmyAQwGERI5sAEvsADQsAEQsQIHsAorWCHYG/RZsAPQsAYQsQQBsAorWCHYG/RZsAMQsAjQsAnQsAAQsAvQsArQMDEBNwcHAyEHIRMHNzcTMwHF8BzvWgKCI/yHcIUbhXL3A2xGm0f9+soCgiabJwKSAAABAB8AAAJbBgAACwBLALAARViwCi8bsQoiPlmwAEVYsAQvG7EEED5ZsgEEChESObABL7AA0LABELECB7AKK1gh2Bv0WbAD0LAG0LAH0LAAELAJ0LAI0DAxATcHBwMjEwc3NxMzAcKZHJiA7nKMHIp/7QN/NJw1/R4Ciy+cLwLZAAEAI/5GBXgFsAATAGiyBhQVERI5ALAARViwAC8bsQAgPlmwAEVYsBAvG7EQID5ZsABFWLAELxuxBBI+WbAARViwDC8bsQwQPlmwAEVYsA4vG7EOED5ZsAQQsQkBsAorWCHYG/RZsg0ADBESObISDgAREjkwMQEBBgYnIic3FjMyNzcBAyMTMwETBXj+/xjXpTtMIzYpgSIH/ki39v3uAbu3BbD6GLbMAhTGDsQoBB/74QWw++IEHgABABH+RgQGBFIAGwBjsgIcHRESOQCwAEVYsAMvG7EDHD5ZsABFWLAALxuxABw+WbAARViwCi8bsQoSPlmwAEVYsBkvG7EZED5ZsgEDGRESObAKELEPAbAKK1gh2Bv0WbADELEWAbAKK1gh2Bv0WTAxAQc2FxYWBwMGBicmJzcWMzI3EzYnJicmBwMjEwGlF4a7oZYWdhjQo0FEIzkngR92BQIHi4Nlje68BDuYrwQD5sT9ILXGAgETxQ+7AtMtKYwFBGr83wQ6AAIAUP/uB4oFxQAXACUAlrIbJicREjmwGxCwFtAAsABFWLAMLxuxDCA+WbAARViwDi8bsQ4gPlmwAEVYsAMvG7EDED5ZsABFWLAALxuxABA+WbAOELEQAbAKK1gh2Bv0WbITAA4REjmwEy+xFAGwCitYIdgb9FmwABCxFwGwCitYIdgb9FmwAxCxGAGwCitYIdgb9FmwDBCxHQGwCitYIdgb9FkwMSEhBwcmJgI3EzYSJDMXFyEHIQMhByEDIQUWNxMmJyYGBwMGFxYWBpT8xcRXnudpFDIctQETpUrPA1Ik/WFGAkUk/b1OAqb7kE97xnNMoNoeLwkGCIERAQSdARChAT2pAQ2SAhPM/m7I/kAZAwwEOw4CAtnC/tNIRnSIAAMAQv/oBtwEUgAgAC8AOQC9sho6OxESObAaELAp0LAaELAz0ACwAEVYsAkvG7EJHD5ZsABFWLAELxuxBBw+WbAARViwHC8bsRwQPlmwAEVYsBcvG7EXED5ZsgcJHBESObI0CRwREjmwNC+yjzQBXbQfNC80AnGxDQewCitYIdgb9FmwFxCxEQGwCitYIdgb9FmyExcJERI5shoJHBESObAcELElAbAKK1gh2Bv0WbAEELEsAbAKK1gh2Bv0WbAJELEwAbAKK1gh2Bv0WTAxEzYSNhcWFhc2FxYSBwchBhYXFjcXBgYnJiYnBicuAjczBxcWFxY2Nzc1JicmBgcBJgYHITc2JyYmVBSY7pRytzGmzsPJGhb9cA1raJqaQUPMe3a1MablisJYEOwFAQ6se6QVBwi0cqAcA/tShTYBpwUHBQhTAiChAQSMAgJeUbQEBP7z14+FnwMFX6A+QQICXE6xBAKO+ZZLLt8HA8alYR3yCAOxpAFTAXqMHC0pQ00AAAEAHgAAAyAGGgANADOyAg4PERI5ALAARViwBC8bsQQiPlmwAEVYsAAvG7EAED5ZsAQQsQkBsAorWCHYG/RZMDEzEzY2FxYXByYnIgYHAx7JF9qqPGIsLC1QaA/KBJ+xygIBF7gMAmNZ+2YAAAIATv/oBS8FwwAaACQAYbINJSYREjmwDRCwHNAAsABFWLASLxuxEiA+WbAARViwAC8bsQAQPlmyCBIAERI5sAgvsBIQsQ0BsAorWCHYG/RZsAAQsRsBsAorWCHYG/RZsAgQsR4BsAorWCHYG/RZMDEFJiQnJyY3NwU2JyYmJyYHJzYhFgQSBwcGAgQnFjY3IQcGFxYWAknT/vsaBAUMFgOvDwoSqouk0R6GAR++AQt3GQ8ey/7WnZHaQ/1FBw4KEJEUBOvUMlRajwFbU4eXAwNJyVQDsP7DxGjN/ryu1wPL0SJOQ2x3AAAB/0r+RgNMBhkAHQB0sgIeHxESOQCwAEVYsBQvG7EUIj5ZsABFWLAPLxuxDxw+WbAARViwHC8bsRwcPlmwAEVYsAUvG7EFEj5ZsBwQsQABsAorWCHYG/RZsAUQsQoBsAorWCHYG/RZsAAQsA3QsA7QsBQQsRkBsAorWCHYG/RZMDEBIwMGBicmJzcWFzI3EyM3Mzc2NhcWFwcmIyIHBzMCocOUE8iiQ0AgNyR4HZehHaAMFdiqNWcqNyekGwvDA4b8NK7GAgISvg4CqQPTtGWyyAIBFrsMxVIAAAIAW//oBiYGLgAaACsAXrIgLC0REjmwIBCwGtAAsABFWLAKLxuxCiA+WbAARViwAC8bsQAQPlmyDQoAERI5sA0vsRMIsAorWCHYG/RZsAoQsR8BsAorWCHYG/RZsAAQsSgBsAorWCHYG/RZMDEFLgInJhISNzYXFhYXNjY3MwIFFhcWAgIHBhM3NiYnJgIPAgYWFhcWEjcCSI/UewgHP5lsq953xUNSZRO1IP7yFQUFPaN1pfQJCoOGrOUjCQgGNXdZpeIoFAOB96F+AVABEleJBAJYUA+Ahf6uR2dlhv6d/ttYewMYarXQBAX+7vVAaW28YQMHAQDzAAACADb/5gUFBKgAGAAnAF6yHSgpERI5sB0QsATQALAARViwBC8bsQQcPlmwAEVYsBUvG7EVED5ZsgcEFRESObAHL7EOCLAKK1gh2Bv0WbAVELEcAbAKK1gh2Bv0WbAEELEjAbAKK1gh2Bv0WTAxEzYSNhcWFhc2NzczBgYHFhcWBwIAJyYCNxcWFhcWNjc3NiYnJgYHBlESnfGUYq8+ZxsOoQ5zbg8DAggl/srd1OAY6gNjWXqoGAcDY2J6phkIAiCgAQaLAgJJTSl8TJCpJ0hHR0n+8f7MBQYBNeVzaX8EA8KpYn2VBAPDrFEAAAEAW//oBq0GAgAaAFayFxscERI5ALAARViwAC8bsQAgPlmwAEVYsBEvG7ERID5ZsABFWLAMLxuxDBA+WbIBAAwREjmwAS+xCAiwCitYIdgb9FmwDBCxFgGwCitYIdgb9FkwMQEHNjY3NwYGBwMGACcuAjcTMwMGFhcWNjcTBTIoancVrRPVzWwi/rnwldxnEaX2pRJ1fYezGacFsN8LiZwB1uIM/aTo/u4EA37kkQPO/DGKngQEmpED1AAAAQBK/+gFYQSUABsAarIUHB0REjkAsABFWLANLxuxDRw+WbAARViwFi8bsRYcPlmwAEVYsAQvG7EEED5ZsABFWLAILxuxCBA+WbIYFgQREjmwGC+xAwiwCitYIdgb9FmyBhYEERI5sAgQsRMBsAorWCHYG/RZMDEBBgYHAyM3BicuAjcTMwMGFxYXFjcTMwc2NjcFYQ+kpZPeFXu5aYs7DHXtdQQDB3aeX4jtH1JSEgSUrqkM/M9rgwQCZLN5Arz9QSUjfAUGhAMKiw1cewAB/w/+SAHcBDoADAApALAARViwDC8bsQwcPlmwAEVYsAQvG7EEEj5ZsQkBsAorWCHYG/RZMDEBAwYGIyInNxYzMjcTAdzDGMyjPUYfNSp/IcIEOvuItcURwRDCBG4AAgA2/+oD9gRQABUAHQBoshAeHxESObAQELAW0ACwAEVYsAAvG7EAHD5ZsABFWLAILxuxCBA+WbIMAAgREjmwDC+wABCxEAGwCitYIdgb9FmyEgwQERI5sAgQsRYBsAorWCHYG/RZsAwQsRgHsAorWCHYG/RZMDEBFhIHBw4CJyYCNzchNiYnJgcnNjYTFhMhBhcWFgJFzuMWBxWa5IPFyBoWApAMammXnEFDzAeoZ/5YDQYIVQROBP7V5jmX/IMDBgEM1Y+DoQMFX6A+QvxdBgELSSlDTwAAAQECBN0DnwYBAAgASgCwBS+yDwUBXbAG0BmwBi8YsADQGbAALxiwBRCwAdCwAS+wBRCwBNCwBC+wAtCwAi+wBRCwB9CwBy+0DwcfBwJdsgMFBxESOTAxARUnJwcHJwEzA5+5da3BAQEtiATuEQObmgQSARIAAAEBDQTgA7wGBQAIACUAsAQvsg8EAV2wAtCwAi+0DwIfAgJdsgAEAhESObAH0LAHLzAxATc3FQEjAzUXAkKp0f7MkunEBWeZBBD+7AEVEAT//wEBBQwDtwWqAAYAcAAAAAEA/gTIA2wF5wAMAC0AsAMvsg8DAV2wANCwAC+0DwAfAAJdsAbQsAYvsAMQsQkCsAorWCHYG/RZMDEBBgYnJiY1FwYzMjY3A2wKuoeEn7AFeENMDAXnhZoEApmAAYxOPQAAAQECBNwCAQXYAAoAHrIACwwREjkAsAgvsg8IAV2xAgWwCitYIdgb9FkwMQE0NjYWFRQGBwYmAQJHbkpHNzZLBVU4RwRFNjlEAgJFAAIA+gSMAqgGKwALABcAMQCwCS+yDwkBXbAV0LAVL7IPFQFdsQMMsAorWCHYG/RZsAkQsQ8KsAorWCHYG/RZMDETNDYzMhYVFAYjIiY3BhYzMjY3NiYjIgb6hV1SeoRdV3ZrBjIrMkkGBjErMkoFUlp/dVRZfXRUKEJILitASQAAAf+o/lEBJAA9AA8AHACwAEVYsAovG7EKEj5ZsQUDsAorWCHYG/RZMDEFBwYHBhcWNxcGJyImNzYlASQvgwcFOBs9DEVVV2kCAwEIAx9WVjkDAReQKwJtVLN2AAABAN4E2wPJBfQAFABDALADL7AI0LAIL7QPCB8IAl2xDgOwCitYIdgb9FmwFNCwANCwAxCwCtCwCi+wC9CwCy+wAxCxEgOwCitYIdgb9FkwMQEGBiMiLgIHBgcnNjYXFhYXFzY3A8kMgV4YLWs0HU8blQqCYDCWIhlRHAXpd4wOPRMBA2UIcpcCAVkEAQNmAAACAKwE0QPpBf8AAwAHAEAAsAIvsg8CAV2wANCwAC+0DwAfAAJdsAIQsAPQGbADLxiwABCwBdCwBS+wAhCwBtCwBi+wAxCwB9AZsAcvGDAxATMBIwMzASMC7/r+ydJW8/70xQX//tIBLv7SAAAC/+7+aQFN/78ACwAXAD8AsBgvsAPQsAMvQA8AAxADIAMwA0ADUANgAwddsA/QsA8vsQkJsAorWCHYG/RZsAMQsRUJsAorWCHYG/RZMDEHNDYzMhYVFAYjIiY3BhYzMjY3NiYnIgYSaktJYWlISmRhBCUdITYGBR4gIzn1TWdiREpmXkYfKzMhHTEBNgAAAf1UBNH+2QYAAAMAIwCwAS+yDwEBXbAA0BmwAC8YsAEQsALQsAIvtA8CHwICXTAxASMDM/7ZtNH8BNEBLwAB/dcE0f/pBgAAAwAjALACL7IPAgFdsAHQsAEvtA8BHwECXbACELAD0BmwAy8YMDEBIQEj/skBIP6+0AYA/tH///z2BNv/4QX0AAcApfwYAAAAAf3WBOX/PQZ/AA4AJgCwDi+wB9CwBy+yAQ4HERI5sQgIsAorWCHYG/RZsg0BDhESOTAxATc3Njc2Jyc3FwQHBgcH/dYOL18JCmsiESgBDAMDoAoE5pIFCzo8BAF8AhahfR5GAAL8vwTk/7MF7gADAAcANwCwAS+wANAZsAAvGLABELAF0LAFL7AG0LAGL7YPBh8GLwYDXbAD0LADL7AAELAE0BmwBC8YMDEBIwMhASMDIf6R3fUBEgHizsABBATkAQr+9gEKAAAB/KD+kf2s/44ACwASALADL7EJDbAKK1gh2Bv0WTAxBTQ2NzYWFRQGBwYm/KBLOjdQSjs6TfU2SQICRDc5RQICRgABAS4E6QKIBkEAAwAXALACL7AA0LAAL7ACELAD0BmwAy8YMDEBMwMjAabixJYGQf6oAAMA6ATcBCMGrwADAA8AGwA/ALANL7AC0LACL7AA0LAAL7QPAB8AAl2wAhCwA9AZsAMvGLANELEHBbAKK1gh2Bv0WbAT0LANELAZ0LAZLzAxATMDIwU0Njc2FhUGBgcGJiU2Njc2FhUUBgcGJgKd6LOX/q1ENzJKAUYzMksCRAFGMzJLRTY0SAav/tYyMEgCAkI0NEQCAkIzNEQCAkI0MEgCAkT//wCeAkIBsQNVAgYAeAAAAAEALgAABKwFsAAFACwAsABFWLAELxuxBCA+WbAARViwAi8bsQIQPlmwBBCxAAGwCitYIdgb9FkwMQEhAyMTIQSI/XXZ9vwDggTk+xwFsAAAAv+qAAAFCQWwAAMABgAwALAARViwAC8bsQAgPlmwAEVYsAIvG7ECED5ZsQQBsAorWCHYG/RZsgYCABESOTAxATMBISUhAwLr7QEx+qEBegLJtwWw+lDKA7kAAwBe/+cFFgXIAAMAFQAlAIayGyYnERI5sBsQsALQsBsQsA3QALAARViwDS8bsQ0gPlmwAEVYsAQvG7EEED5ZsgIEDRESOXywAi8YsmACAV2yQgIBXbJyAgFdtNAC4AICXbIwAgFdsgACAXGxAQGwCitYIdgb9FmwDRCxGgGwCitYIdgb9FmwBBCxIgGwCitYIdgb9FkwMQEhNyEBLgInJhISNzYEABcWAgIHBhM3NiYnJgIPAgYWFxYSNwOQ/ksjAbT+mo/WeggHOp90qAGwAQEMBjmLZ7LcCQeDg6/iIgoICoSFpeIoAnnC/LEDg/idcwFRASFaggj+3vd8/r/+81qcAxlqvMkEBf7t7Udpt9IEBwEA8wAB/7IAAAR/BbAABgAxALAARViwAy8bsQMgPlmwAEVYsAEvG7EBED5ZsABFWLAFLxuxBRA+WbIAAwEREjkwMQEBIQEzEyEC3v3l/u8C6+/z/wAEQfu/BbD6UAADAAAAAASIBbAAAwAHAAsATgCwAEVYsAgvG7EIID5ZsABFWLACLxuxAhA+WbEAAbAKK1gh2Bv0WbIFCAIREjmwBS+xBgGwCitYIdgb9FmwCBCxCgGwCitYIdgb9FkwMTchByETIQchEyEHISQDpiP8WfQC4SP9HzgDfyP8gMrKA03GAynMAAEALgAABYMFsAAHADkAsABFWLAGLxuxBiA+WbAARViwAC8bsQAQPlmwAEVYsAQvG7EEED5ZsAYQsQIBsAorWCHYG/RZMDEhIxMhAyMTIQSG9tn9lNn2/ARZBOT7HAWwAAH/3AAABJ8FsAAMAD4AsABFWLAILxuxCCA+WbAARViwAy8bsQMQPlmxAQGwCitYIdgb9FmwBdCwCBCxCgGwCitYIdgb9FmwB9AwMQEBIQchNwEBNyEHIQEDG/4vAp4j/BccAiD+qBkDxiT9dgErAtH9+cqiAkMCPo3M/gEAAAMAUgAABasFsAAUABsAIgBxshIjJBESObASELAZ0LASELAg0ACwAEVYsBMvG7ETID5ZsABFWLAJLxuxCRA+WbIAEwkREjmwAC+yIBMJERI5sCAvsQgBsAorWCHYG/RZsAvQsAAQsBLQsCAQsBjQsAAQsRkBsAorWCHYG/RZsB/QMDEBFhIHBwYGBAcHIzcmADc2EiQ3NzMBBhYXEwYGBTYmJwM2NgPc0P8FAw+q/umqJPck4P8AFg+tARmoJvf9WBN9hoKaxANDEnqFgZ29BPwL/uvTMKD8jgWqqwwBLOSiAQGPBLP9F5KsDQKyCr6Nl6wP/U8MuwAAAQB1AAAF1wWwABkAXbIKGhsREjkAsABFWLAELxuxBCA+WbAARViwEC8bsRAgPlmwAEVYsBgvG7EYID5ZsABFWLAKLxuxChA+WbIXBAoREjmwFy+wANCwFxCxDAGwCitYIdgb9FmwCdAwMQE2NjcTMwMGAAcDIxMmAjcTMwMGFxYWFxMzA0GGqxlV91Yq/sH2SPZI3NsdU/ZUCAMFY1me9AI/G8WaAff+Avn+1Rf+iQF3HwFB6AHx/g4+PGKHGANtAAABAAkAAAT3BccAIwBbsgAkJRESOQCwAEVYsBkvG7EZID5ZsABFWLAPLxuxDxA+WbAARViwIi8bsSIQPlmxIQGwCitYIdgb9FmwANCwGRCxBwGwCitYIdgb9FmwABCwDtCwIRCwEdAwMSU2EhM3NQInJgYCBwYWFwchNzcCEzc2EiQXFhYSBwcCBTcHIQKAj6shBgvPkL4+AwVRUSD+FCXRoSUNGrQBEqSd4GYVDTX+9tEk/h3OJwEzATdPMwEPCAXb/nx2kK8Z0MsCAQ4BEl24ASafBASk/t6oV/6e0QTLAAIAPv/qBDMEUQAdACsAfLIaLC0REjmwGhCwJNAAsABFWLAaLxuxGhw+WbAARViwAC8bsQAcPlmwAEVYsBAvG7EQED5ZsABFWLAKLxuxChA+WbEFAbAKK1gh2Bv0WbINGhAREjmyHBoQERI5sBAQsSMBsAorWCHYG/RZsBoQsSgBsAorWCHYG/RZMDEBAwYXFhczNxcGJyYmJwYGJyYmJyY3NzYSNhcWFzcBBhcWFhcWNxMmJyYGBwQzgAcCAicODQY1QE5eDTyUZJq0BwMGAxWLzICtVTH9zAYBAllShGJQL395nhYEOv0GNBo0AgO3HQICVEtLWQIC27U9PBWsAROGAwSVhf24MzhkdAIDiwHJiQQF07YAAv/l/ncEawXHABQAKQBoshQqKxESObAUELAc0ACwDy+wAEVYsAAvG7EAID5ZsABFWLAMLxuxDBA+WbIVAAwREjmwFS+xJwGwCitYIdgb9FmyBScVERI5sAAQsRsBsAorWCHYG/RZsAwQsSEBsAorWCHYG/RZMDEBFhYHBgcWFgcOAicmJwMjEz4CEzY2NzYmJyYGBwMWFzI2NzYmJyc3Atu42A0O3F5eCAqG24SddFfs9xCS4hdpggsJWFFgkRKLSpFxoxAOWViEGgXEBNWpw3UuunWF0W8DBFL+NgWod8Rt/ZQCdGlYbgQCgGb83lACj3JljAUBuAABAHf+XwQwBDoACAA4sgAJChESOQCwAEVYsAEvG7EBHD5ZsABFWLAHLxuxBxw+WbAARViwBC8bsQQSPlmyAAcEERI5MDEBATMBAyMTAzMByQFp/v3fTu1TsOwBPgL8++L+QwHeA/0AAAIAOP/nBDgGJAAfAC4AZbICLzAREjmwAhCwJtAAsABFWLADLxuxAyI+WbAARViwFS8bsRUQPlmwAxCxCAGwCitYIdgb9FmyDhUDERI5sA4vsSsBsAorWCHYG/RZshwrDhESObAVELElAbAKK1gh2Bv0WTAxATY2FxYXByYHIgYHBhcXBAMHDgInLgI3NjY3NSYmAwYXFhYXFjY3NiYnBgYHAUEH67FsmRWEakxrCg9wLAGGJwMUme+QisRcDhLbnkhNBwYDA2NXd6QcDmZgeqUYBOKVrQICMcQ4AkE3TTcUrP51FJ36iAQEh/GUvv8cDyeG/XM1O2h9AwO9vH+7HgO6qgAAAQAo/+oEAgRRACcAo7IUKCkREjkAsABFWLAILxuxCBw+WbAARViwJS8bsSUQPlmyFQglERI5sBUvso8VAV20HxUvFQJxtF8VbxUCcbS/Fc8VAl207xX/FQJxsloVAV2xFwewCitYIdgb9FmyAhcVERI5sAgQsQ8BsAorWCHYG/RZsgwVDxESObYMDBwMLAwDXbAlELEdAbAKK1gh2Bv0WbIgFx0REjm0AyATIAJdMDETNjcmJjc2JBcWFhUnNCYjJgYHBhcXByciBgcGFhcWNjczDgInJiYvCuY9TwIFAQ3OstvpZU5ZhgoTsdEftG6ECQhnXFqODu4Jgt1+w+wBKbdTIW1Imq4EBbKQAUJIAlBEeQYBrQFVSj9OAwJVSmucUAIEqgAAAQBm/n0EUAWwABsAUbISHB0REjkAsAwvsABFWLAALxuxACA+WbEZAbAKK1gh2Bv0WbIBGQAREjmyAgwAERI5shMMABESObATELEGAbAKK1gh2Bv0WbIYAAwREjkwMQEHAQYXFhcXFhYHBgcnNzY3NicnJBM2EjcBITcEUBz+FuIHA12wWUkECt56Kz8LCk51/u8cDqqxART93iIFsJz+CfTZXiQ9IWFJpaRrL0g6NxwkWwENigEqsgEPwwABABH+YQQGBFIAEgBUsggTFBESOQCwAEVYsAMvG7EDHD5ZsABFWLAALxuxABw+WbAARViwBy8bsQcSPlmwAEVYsBAvG7EQED5ZsgEDEBESObADELENAbAKK1gh2Bv0WTAxAQc2FxYWBwMjEzYnJicmBwMjEwGlFIq1oZUTu+28BQMOhohlie68BDuFnAQE1MD7qwRULCeAAwR9/O4EOgAAAwBs/+cEPwXJABEAGQAiAImyICMkERI5sCAQsADQsCAQsBjQALAARViwCS8bsQkgPlmwAEVYsAAvG7EAED5ZshMJABESOXywEy8YsmATAV2yQhMBXbJyEwFdtNAT4BMCXbIwEwFdsgATAXGwCRCxFwGwCitYIdgb9FmwExCxGgGwCitYIdgb9FmwABCxIAGwCitYIdgb9FkwMQUmAjc0NzcSABcWEgcGBwcCAAEhNjUmJyYDASEGFxQWFxYTAei4xAIJHzEBHt+5wgEBCSI0/uf+tgHJFQWf2UsBn/43FQFUTtZOFAQBBetLR8wBQgFJBQT+/OdLR93+xf68A1GDUe8HCP6i/s2DS3mCAwwBZAABAG7/9AIKBDoADQApALAARViwAC8bsQAcPlmwAEVYsAkvG7EJED5ZsQQBsAorWCHYG/RZMDEBAxUWFzI3BwYnJiY3EwHrgwNLJy0QSkt8ew2DBDr89S1AAwm+FgICo4kDFgAAAf+o//AD1gX7ABoAU7IPGxwREjkAsAAvsABFWLALLxuxCxA+WbAARViwEC8bsRAQPlmwCxCxBgGwCitYIdgb9FmyDwAQERI5shIQABESObAAELEWAbAKK1gh2Bv0WTAxARYXExYWFzM3BwYjJiYnAwEhAScmJicnBzc2AZm4MOgIHiQSEQ0qKl9yHWn+lv70AjEuCyorGxsOPgX5BKX7xB82BQHDCAJmawIE/TkEHcAoLQIBAbgPAAEAPv51BCYFxQAtAFmyBS4vERI5ALAXL7AARViwKy8bsSsgPlmxAgGwCitYIdgb9FmyBy4rERI5sAcvsQoBsAorWCHYG/RZsh4XKxESObAeELEQAbAKK1gh2Bv0WbIlCgcREjkwMQEmIyIGBwYFFwcnIgYHBhYfAhYHBgYHJzc2NzYnJyYnJhM2NjcmJjc2JDMyFwPue1h8mAwbAQ+FI36s0xILYWCELqkIBXhsgC9CCQc/KqBC2hUKuKtUYAQIAR/bjIgE2iZbTq8CAcYBmY5dgxwlDzyQUqlNajFIPTIZDzMjcgEBjcs4KIlYrsYuAAEAXf/1BNoEOgAWAF6yDRcYERI5ALAARViwFS8bsRUcPlmwAEVYsAsvG7ELED5ZsABFWLARLxuxERA+WbAVELEAAbAKK1gh2Bv0WbALELEGAbAKK1gh2Bv0WbAAELAP0LAQ0LAT0LAU0DAxASMDBhcWFzI3BwYjJiY3EyEDIxMjNyEEuZtjAwIGSiYvEUVQfHsNYv7Am+2bpyIEWwN8/bQZFEEDCb4VAqOKAlj8hAN8vgAC/8v+YAQMBFMAEgAgAFKyDiEiERI5sA4QsBbQALAARViwBS8bsQUcPlmwAEVYsBEvG7EREj5ZsABFWLAOLxuxDhA+WbEVAbAKK1gh2Bv0WbAFELEdAbAKK1gh2Bv0WTAxEzY2NzYXFhYXFgcHBgYnJicDIwEWFxY2NzYnJiYnJgYHdRBbSJDQsMgJAwcNLPexqWFh7gFrNIN1nhULAwhVTmuOGQI+b8lJlAUE6cdFRVPf+AUEdv37Ar9vBAOzn3U9cWwDAr+iAAEAO/6JA/AEUwAgAFyyDSEiERI5ALAARViwAC8bsQAcPlmwAEVYsBovG7EaED5ZsABFWLATLxuxExg+WbAAELADsAorWNgb3FmwABCxBwGwCitYIdgb9FmwGhCxDQGwCitYIdgb9FkwMQEWFgcnNiYnJgYHBwYXFxYHBgYHJzc2NzYmJyYCNzcSAAJztMkI3gVVVHOhFgQc7m2gBwN7bHkpQwkEJTrNvxMCHQExBE4E4bQBZG4EA8CjI+1XJz2PUatNayxKPyEoED4BBMQUAQIBNQAAAgA4/+gEtgQ7ABEAIgBkshgjJBESObAYELAH0ACwAEVYsBAvG7EQHD5ZsABFWLARLxuxERw+WbAARViwCC8bsQgQPlmwERCxAAGwCitYIdgb9FmwCBCxFwGwCitYIdgb9FmwEBCxIAGwCitYIdgb9FkwMQEFFgcHDgInLgI3NzYAMwUBBhcWFhcWNjc3NicmJicmBgSS/v6DEQMQlu+Ki8RZEAIiATHeAjv8gAYCBGBXb50cBwYCBV5VeKADdgOrxxaR7YUEApD8lRD7ASEB/dE2PW58AgOspS80OmZ3AwO2AAEAbv/rBCMEOgARAEuyAxITERI5ALAARViwEC8bsRAcPlmwAEVYsAovG7EKED5ZsBAQsQABsAorWCHYG/RZsAoQsQUBsAorWCHYG/RZsAAQsA7QsA/QMDEBIQMHFDMWNxcGJyYmNxMhNyEEAf6jZQI/IT0VUl98eg5h/rciA5MDef2vKEoBFbQrAgKrlgJJwQAAAQBX/+UD/gQ8ABYAPbIQFxgREjkAsABFWLAKLxuxChw+WbAARViwAC8bsQAcPlmwAEVYsBEvG7ERED5ZsQUBsAorWCHYG/RZMDEBAwcGFhcWEgMmJxcWBwYCBicmJjc3EwG/bQUCOzmVww4GIeI6Cw+b+JmpuAoDbgQ6/WtMTF8CBgF0ASSBfQGp1/v+xqEEA9fAJgKRAAACADL+IgVtBEQAGwAkAFuyGSUmERI5sBkQsBzQALAaL7AARViwEi8bsRIcPlmwAEVYsAcvG7EHHD5ZsABFWLAALxuxABA+WbAZ0LEcAbAKK1gh2Bv0WbAO0LASELEiAbAKK1gh2Bv0WTAxBSYnJjc2EjcXBgIXFhYXEzY2Fx4CBwYABQMjATY2JyYmBwYHAfrucmgZE5uGiHFuDApxYHEOpnuH0WYOGv6v/vNX7QFercoCA2dWNgwMI6qc4KABCVuWaP70fWOGGgKFdZMCApD1jfT+0Rr+MQKRJPGrgZAGBDYAAQA//iIFigQ8AB0AU7IOHh8REjkAsA8vsABFWLAALxuxABw+WbAARViwCC8bsQgcPlmwAEVYsBUvG7EVHD5ZsABFWLARLxuxERA+WbAO0LEBAbAKK1gh2Bv0WbAc0DAxAQM2NhInJicXFhcSBwYFAyMTJgI3EzMDBhcWFhcTA56jkr9EDAkj3isKH++p/vRX7Vfh2SBS7VIJAwNnX6IEOvx6IrcBDqt+eAJ2f/5E4Z8Z/jIB0iIBRPcB6f4UQkBrjhwDgwAAAQBU/+QGEAQ9ACsAYLIjLC0REjkAsABFWLAALxuxABw+WbAARViwGy8bsRscPlmwAEVYsCEvG7EhED5ZsABFWLAmLxuxJhA+WbEHAbAKK1gh2Bv0WbIMIQAREjmwIRCxEgGwCitYIdgb9FkwMQEHBgYHBhYXFjY3EzMDBhcXFhcWNjc3NicmJxcWFxYCBicmJicGJyYmNxATAihST0YDA0M9W30TNfU0CQMCEHJWeRwKEQwMLeI0DBNy5qRqmBiF06KsAt4EOZiV6IN3ewMGoJkBRv66SzEbmAMEqapAgoKBfAN4gt3+WdUEAnhl5gcE6dcBXwErAAACAFD/5gSNBckAHgAoAG6yFCkqERI5sBQQsCDQALAARViwGS8bsRkgPlmwAEVYsAYvG7EGED5ZsiEZBhESObAhL7ETAbAKK1gh2Bv0WbAC0LIMGQYREjmwBhCxEAGwCitYIdgb9FmwIRCwHdCwGRCxJQGwCitYIdgb9FkwMQEGBwcGBCcuAjcTNwMGFhcWEzcmAjc2NhcWFgcDNwEGFxM3NCcmBgcEgjlLEyX+58h+vFsPL+cwDmRhyjQUt8sOE9yfmKESNHL98RK6OARUOUoLAlYTC3Xh/AYDedeAASMC/tp4jgMHASBvLAEVu7/RBATZrf7LGAEh4UwBODdwAgJUTQABAG0AAAUGBckAGABWsgwZGhESOQCwAEVYsAQvG7EEID5ZsABFWLAWLxuxFiA+WbAARViwDC8bsQwQPlmyABYMERI5sAQQsQgBsAorWCHYG/RZsBYQsREBsAorWCHYG/RZMDEBATY2FxYXBycGBwEDIxMDJicmByc2MxYXAkcBEz+KVztRNTNBLP5oWfZepxU4ESURPECvPwMJAed5YAICGcMGA0X9Xf38Ah8CiT4DAQXEGATLAAIAV//jBn0EOgAUACoAaLIJKywREjmwCRCwIdAAsABFWLATLxuxExw+WbAARViwDC8bsQwQPlmwExCxAQGwCitYIdgb9FmwDBCwB9CyChMMERI5sAEQsBfQsBLQsAwQsR0BsAorWCHYG/RZsiEMEhESObAn0DAxAScXBgIGBicmJicGJyYmNxI3BzchASYnJQYGBwYXFjY3NzMHBhcWFxYTNgZaeAMCPHixb2ucGIbamKEGBHhyIgX0/n4BB/zdSDwGC3Bbfhgk9CIIAwqBkzYbA4MBpIr+29xtAwJ4aesHBOvdAQDQArb+plFSAonXfPYGB5ad6eNJNbIDBAEplwABAJH/8QWFBbAAGQBxsgEaGxESOQCwAEVYsBgvG7EYID5ZsABFWLAKLxuxChA+WbAARViwFC8bsRQQPlmwGBCxAAGwCitYIdgb9FmyBBgUERI5sAQvsAoQsQsBsAorWCHYG/RZsAQQsREBsAorWCHYG/RZsAAQsBbQsBfQMDEBIQM2FxYWBwYEBzc2Njc2JicmBwMjEyE3IQTf/iJNjW/f9hES/sj+E4ujDw1yeW6SdvfZ/ockBE4E5P5zJwIC88rZ8QK/BIl6boEEAyD9cwTkzAABAGf/6AURBccAIACIshQhIhESOQCwAEVYsAwvG7EMID5ZsABFWLADLxuxAxA+WbIADAMREjmyEAMMERI5sAwQsRMBsAorWCHYG/RZshYMAxESOXywFi8YsmAWAV2ychYBXbJCFgFdsjAWAV200BbgFgJdsgAWAXGxGQGwCitYIdgb9FmwAxCxHQGwCitYIdgb9FkwMQEGACcuAicmEhIkFxYSFyMmJicmBgclByEHBhYXFjY3BKkh/q/wi9F3BwZEwgEcqdj8C/UFe3aW1D0B9CT+GQkGfnyLtiQB2+P+8AQDfu+acQGJATmeAwT++OueiwMF0+sBymKkuQQGl5MAAv/KAAAH9QWwABkAIgB9sgojJBESObAKELAb0ACwAEVYsBgvG7EYID5ZsABFWLAILxuxCBA+WbAARViwEC8bsRAQPlmyARgIERI5sAEvsBgQsQoBsAorWCHYG/RZsBAQsRIBsAorWCHYG/RZsAgQsRwBsAorWCHYG/RZsAEQsSIBsAorWCHYG/RZMDEBBR4CBwYAIyETIQMHAgIHIzc3NjY3NxMhAwMFNjY3NiYnBSABEYrUZgsR/sX0/d/Z/lJxHkP7wlsWJH+iKROKA5F/WwESf7ASD3FpA6EBBHbMgtP++wTk/fWS/s/+7wXKAQnf928Cl/0m/fQCApR9bogEAAIALgAAB/0FsAASABsAhbIBHB0REjmwARCwFNAAsABFWLACLxuxAiA+WbAARViwES8bsREgPlmwAEVYsAsvG7ELED5ZsABFWLAPLxuxDxA+WbIBAgsREjmwAS+yBQILERI5sAUvsAEQsQ0BsAorWCHYG/RZsAsQsRUBsAorWCHYG/RZsAUQsRsBsAorWCHYG/RZMDEBIRMzAxcWFgcGBCMhEyEDIxMzAQMFNjY3NiYnAbUCa2z2Yfzi/g8Q/sb0/d9u/ZVu9vz2At5VARKBrg8OcWsDRQJr/dIBAfHDzv4Cev2GBbD9CP4YAgKMc2h8BAAAAQCgAAAFmAWwABYAX7IBFxgREjkAsABFWLAVLxuxFSA+WbAARViwCC8bsQgQPlmwAEVYsBEvG7ERED5ZsBUQsQABsAorWCHYG/RZsgQVCBESObAEL7EOAbAKK1gh2Bv0WbAAELAT0LAU0DAxASEDNhcWFgcDIxM2JyYnJgcDIxMhNyEE4f4gRoKG6usYS/dMCAcVvmSvffbZ/pUkBEEE5P6aHAIE9df+OAHJQDCOBgMc/UwE5MwAAAEAJf6YBXwFsAALAEkAsAkvsABFWLAALxuxACA+WbAARViwBC8bsQQgPlmwAEVYsAYvG7EGED5ZsABFWLAKLxuxChA+WbECAbAKK1gh2Bv0WbAD0DAxATMDIRMzAyEDIxMhASL32gJs2vf9/lk/9z/+RAWw+xoE5vpQ/pgBaAAAAgAjAAAEoQWwAAwAFQBhsg8WFxESObAPELAJ0ACwAEVYsAsvG7ELID5ZsABFWLAJLxuxCRA+WbALELEAAbAKK1gh2Bv0WbIDCwkREjmwAy+wCRCxDwGwCitYIdgb9FmwAxCxFQGwCitYIdgb9FkwMQEhAxcWFgcGBCMhEyEBAwU2Njc2JicEff12Pf7j/REQ/sf0/d38A4L88lYBEoGuDw5wawTk/p8BAe/E0P4FsP0I/hICApB3aXkEAAAC/4T+mgWRBbAADgAVAFeyEhYXERI5sBIQsAvQALABL7AARViwCy8bsQsgPlmwAEVYsAIvG7ECED5ZsAEQsATQsAIQsQ0BsAorWCHYG/RZsBDQsAbQsAsQsREBsAorWCHYG/RZMDEBIxMhAyMTFzYSExMhAzMFJRMhAwcCBP7rPvxgP+5bZXOeN4gDfdm0+/YCX7f+Zm4RVf6aAWb+mgIwA1MBMwEOAlX7GgQEBBr+GkL+vAAB/6UAAAfgBbAAFQB+ALAARViwCS8bsQkgPlmwAEVYsA0vG7ENID5ZsABFWLARLxuxESA+WbAARViwAi8bsQIQPlmwAEVYsAYvG7EGED5ZsABFWLAULxuxFBA+WbIQCQIREjmwEC+xAAGwCitYIdgb9FmwBNCyCBAAERI5sBAQsAvQshMAEBESOTAxASMDIxMjASEBASETMxMzAzMBIQEBIQTko272bp3+Of6+Alj+0gEb6Z1q9mqKAbcBOf3bATf+3QJ0/YwCdP2MAxMCnf2gAmD9oAJg/U39AwAAAQAe/+0EqAXFACkAhLIHKisREjkAsABFWLAOLxuxDiA+WbAARViwGi8bsRoQPlmyAA4aERI5sAAvsh8AAXGynwABXbJ6AAFdskoAAV2wDhCxBgGwCitYIdgb9FmyCg4aERI5sAAQsScBsAorWCHYG/RZshMnABESObIdDhoREjmwGhCxIQGwCitYIdgb9FkwMQEyNjc2JicmBgcHPgIXFhYHBgUWFgcGBAcHJiQ3FwYWFxY2NzYvAjcCd36hDAx9bWeiEfUJjviM4PgOEf79Y1wHDP7Z5TXS/v8H8wSCZn7BDhvRJLUjA0l4al5wAgJwYQF3umkCBdi5z3gurGy76wwBAue/AWR5AgSBbsUZAwHIAAEAJwAABXwFsAAJAEUAsABFWLAALxuxACA+WbAARViwBy8bsQcgPlmwAEVYsAIvG7ECED5ZsABFWLAFLxuxBRA+WbIEAAIREjmyCQACERI5MDEBMwMjEwEjEzMDBH3//fey/Ov+/feyBbD6UAP+/AIFsPwBAAAB/8oAAAV8BbAAEQBPsgQSExESOQCwAEVYsAAvG7EAID5ZsABFWLABLxuxARA+WbAARViwCS8bsQkQPlmwABCxAwGwCitYIdgb9FmwCRCxDAGwCitYIdgb9FkwMQEDIxMhAwcCAgcjNzc2Njc3EwV8/fbZ/lJxHkT8w1gWIn6hKhaKBbD6UATk/fWS/sv+8ALKAgfU8IIClwAAAQCb/+cFUwWwABAARLIAERIREjkAsABFWLABLxuxASA+WbAARViwDy8bsQ8gPlmwAEVYsAYvG7EGED5ZsgABBhESObELAbAKK1gh2Bv0WTAxAQEhAQYGJyInNxY3Mjc3ASEClwGfAR39TVTCfy9BFzQfbkNE/tcBAgK4Avj7VZuDAgfIBwFsfAQWAAADAFb/xAYTBewAGQAiACwAY7IXLS4REjmwFxCwH9CwFxCwJNAAsAwvsBkvsgAZDBESObAAL7IkDBkREjmwJC+xCwGwCitYIdgb9FmwDtCwABCwF9CwFy+wJBCwHtCwHi+wABCxIwGwCitYIdgb9FmwH9AwMQEyFhcWBwYCBwYnJwcjNyImAjc2EiQXFzczAQYWFxcTIwYGJQMzMjYSJyYmJwQMoOs8QBEQr4udtRoo7Sip7XMQEbkBKKscKub9IBSLkh2TFqjkApCREYbPYBIPjG4FJJKCjKqk/vFPWQMBv8CYAQ6eqgEZnAEBx/zcqcoIAQMRAt3e/O+SAQSIb38EAAABACX+oQV8BbAACwA8ALAJL7AARViwAC8bsQAgPlmwAEVYsAQvG7EEID5ZsABFWLAKLxuxChA+WbECAbAKK1gh2Bv0WbAG0DAxATMDIRMzAzMDIxMhASL32gJs2vfZq3TjPfvxBbD7GgTm+xz91QFfAAABAMUAAAVqBbAAEABHsgUREhESOQCwAEVYsAAvG7EAID5ZsABFWLAJLxuxCSA+WbAARViwAS8bsQEQPlmyDQEJERI5sA0vsQUBsAorWCHYG/RZMDEBAyMTBicmJjcTMwMGFgQ3EwVq/fZrmq3m8BlM9kwQYAEGznwFsPpQAj4sBALz3AHJ/jaAggYqAqgAAQArAAAHYwWwAAsASQCwAEVYsAAvG7EAID5ZsABFWLADLxuxAyA+WbAARViwBy8bsQcgPlmwAEVYsAkvG7EJED5ZsQEBsAorWCHYG/RZsAXQsAbQMDEBAyETMwMhEzMDIRMCH9kBrdn32gGq2vb9+cX8BbD7GgTm+xoE5vpQBbAAAAEAK/6iB2MFsAAPAFUAsAsvsABFWLAALxuxACA+WbAARViwAy8bsQMgPlmwAEVYsAcvG7EHID5ZsABFWLANLxuxDRA+WbEBAbAKK1gh2Bv0WbAF0LAG0LAJ0LAK0LAC0DAxAQMhEzMDIRMzAzMDIxMhEwIf2QGt2ffaAara9tulctk9+gz8BbD7GgTm+xoE5vsS/eABXgWwAAACAIkAAAWdBbAADAAVAGGyARYXERI5sAEQsA3QALAARViwAC8bsQAgPlmwAEVYsAkvG7EJED5ZsgMACRESObADL7AAELELAbAKK1gh2Bv0WbAJELEPAbAKK1gh2Bv0WbADELEVAbAKK1gh2Bv0WTAxEyEDFxYWBwYEIyETIQEDBTI2NzYmJ6sCdWD94f8PEP7H9v3f2/6AAhRWARKArw8NbW0FsP3TAQHsxtH+BO39y/4SAZF3Z3sEAAADAC4AAAa9BbAACgATABcAcrIGGBkREjmwBhCwD9CwBhCwFdAAsABFWLAJLxuxCSA+WbAARViwFi8bsRYgPlmwAEVYsAcvG7EHED5ZsABFWLAULxuxFBA+WbIBCQcREjmwAS+wBxCxDQGwCitYIdgb9FmwARCxEwGwCitYIdgb9FkwMQEXFhYHBgQjIRMzAwMFNjY3NiYnASMTMwHB/uP9ERD+x/T93f33hFYBEoGuDw5wawL19v32A4MBAe/E0P4FsP0I/hICApB3aXkE/UkFsAACACMAAASUBbAACgATAFKyDRQVERI5sA0QsAfQALAARViwCS8bsQkgPlmwAEVYsAcvG7EHED5ZsgEJBxESObABL7AHELENAbAKK1gh2Bv0WbABELETAbAKK1gh2Bv0WTAxARcWFgcGBCMhEzMDAwU2Njc2JicBtv7j/REQ/sf0/d3994RWARKBrg8OcGsDgwEB78TQ/gWw/Qj+EgICkHdpeQQAAAEAT//pBPcFyAAgAIiyDiEiERI5ALAARViwFC8bsRQgPlmwAEVYsB0vG7EdED5ZsQMBsAorWCHYG/RZsggUHRESOXywCC8YsjAIAV2ycggBXbLiCAFdskIIAV2yYAgBXbLQCAFdsgAIAXGxBwGwCitYIdgb9FmwFBCxDQGwCitYIdgb9FmyERQdERI5siAdFBESOTAxARYWFxY2NwU3ITc2JicmBgcHNgAXHgIXFgICBCcmACcBQwd+fJTOOv4FJAHuCAODfoqwI/UoAUvrjtR5CQZHvf7sp97+/QgB2puIAwXW7AHMZJ+2BASalAHmARQEA37xmHj+c/7RnQMEAQXlAAIAMv/nBvkFxwAYACgAhrINKSoREjmwDRCwJNAAsABFWLAILxuxCCA+WbAARViwEC8bsRAgPlmwAEVYsAYvG7EGED5ZsABFWLAALxuxABA+WbIKCAYREjl8sAovGLIfCgFxtGAKcAoCXbEEAbAKK1gh2Bv0WbAQELEeAbAKK1gh2Bv0WbAAELElAbAKK1gh2Bv0WTAxBSYAETcjAyMTMwMzNhI3NhcWABcWAgIHBhM3NiYmJyYGAgcGFhcWEjcEL+P+/AG4afb99nKsJ++ub3zYAQEMBjmLZ7LaCQYyd1t+w3kKCoSEreEjFAUBPAEJJ/2jBbD9ceIBVEQsAwT+3vd8/r/+81qcAxhqbblhAwSW/s7nt9IEBQEO9QAAAv+wAAAE0wWxAA4AFwBjshIYGRESObASELAL0ACwAEVYsA0vG7ENID5ZsABFWLAALxuxABA+WbAARViwAy8bsQMQPlmyEw0AERI5sBMvsQEBsAorWCHYG/RZsgUTARESObANELEUAbAKK1gh2Bv0WTAxIRMhASEBJiY3PgIzBQMBBhYXFxMnIgYC31/+9/6Q/usBsWdYCguX/p4B6f39yg9rc/FZ14atAiD94AJvQcV3jc1rAfpQA+FxhwQBAgACiwACAEP/5gRhBhMAGwArAGSyGCwtERI5sBgQsB3QALAARViwEy8bsRMiPlmwAEVYsAYvG7EGED5ZsgATBhESObAAL7IXABMREjmyERMXERI5shoABhESObEcAbAKK1gh2Bv0WbAGELElAbAKK1gh2Bv0WTAxARYSBwYAJy4CNzc1NxIANzc2NzMOAgQGBzYXJgYGBwYXFhYXFjY3NzYmAp66zxIW/tHgi8dbEAIKMQEj516TFcEIUpr+179BnoBPfU0LBwQHYlh1oBUCDWcD/gT+7Nf3/s4EBI75lhUDSwFQAY4yEh1mZIBTOaSXmMQCTYxbSjpkcwMDsKAVi6AAAAMAIgAABBYEOgAOABcAHwCRshkgIRESObAZELAO0LAZELAR0ACwAEVYsAEvG7EBHD5ZsABFWLAALxuxABA+WbIYAAEREjmwGC+yjBgBXbRfGG8YAnG07xj/GAJxtL8YzxgCXbQcGCwYAnGyWhgBXbEPB7AKK1gh2Bv0WbIIDxgREjmwABCxEAGwCitYIdgb9FmwARCxHwGwCitYIdgb9FkwMTMTBRYXFgcGBxYWBwYGBwMDFzY2NzYmJycXMjc2JicnIrwBnpRipAkK0FRhAgXpzMwv9GFvCQpHUvK21BYJTWTLBDoBBCtJqqBRGXpWlKYDAc3+8wEDSkE5QwOvAYI6PwMBAAABABgAAAOJBDoABQAsALAARViwBC8bsQQcPlmwAEVYsAIvG7ECED5ZsAQQsQABsAorWCHYG/RZMDEBIQMjEyEDZv45mu28ArUDdvyKBDoAAAL/hf6+BGQEOgAOABQAXbISFRYREjmwEhCwBNAAsAwvsABFWLAELxuxBBw+WbAARViwCi8bsQoQPlmxAAGwCitYIdgb9FmwBtCwB9CwDBCwCdCwBxCwD9CwENCwBBCxEQGwCitYIdgb9FkwMTc2NjcTIQMzAyMTIQMjEwUlEyEDAjFqgR9OAtuakVrsOP1hOPFbAWgBlXb++TY/v2HvqgGB/Ij9/AFC/r4CAwMEAqf+9f70AAH/rQAABnIEOgAVAIMAsABFWLAJLxuxCRw+WbAARViwDS8bsQ0cPlmwAEVYsBEvG7ERHD5ZsABFWLACLxuxAhA+WbAARViwBi8bsQYQPlmwAEVYsBQvG7EUED5ZshARAhESObAQL7KPEAFdsQABsAorWCHYG/RZsATQsggQABESObAQELAL0LITABAREjkwMQEjAyMTIwEhAQMhEzMTMwMzASEBEyED/4NM7Uxz/sL+zwHI6wETpHRK7UpnATkBMP5T+P7oAbP+TQGz/k0CPwH7/lcBqf5XAan98P3WAAEAFv/pA7wEUAApAKayGSorERI5ALAARViwJi8bsSYcPlmwAEVYsAovG7EKED5ZshkmChESObAZL7TvGf8ZAnG0HxkvGQJxsr8ZAXG0XxlvGQJxtL8ZzxkCXbKMGQFdsloZAV2xFgewCitYIdgb9FmyAxYZERI5sAoQsREBsAorWCHYG/RZsg4WERESObQDDhMOAl2wJhCxHwGwCitYIdgb9FmyIhkfERI5tAwiHCICXTAxAQYGBxYWBw4CJyYmNzMGFjMyNjc2Jyc3FzY2NzYmIyYGBwc2NhceAgO2BV5mSEUEBXzXfbDbBOkCYlBXeQsVpbgfnFVnCQdPREtzD+0M+bhzsFwDGkp2MyF9T2mXUQMCvZdFVlVIhwUBrwECSUQ/RwJNQQGUtQICSokAAQAZAAAESAQ6AAkARQCwAEVYsAAvG7EAHD5ZsABFWLAHLxuxBxw+WbAARViwAi8bsQIQPlmwAEVYsAUvG7EFED5ZsgQHAhESObIJBwIREjkwMQEzAyMTASMTMwMDVPS87Xz98vS87XwEOvvGAsL9PgQ6/T4AAAEAIgAABIEEOgAMAGkAsABFWLAELxuxBBw+WbAARViwCC8bsQgcPlmwAEVYsAIvG7ECED5ZsABFWLALLxuxCxA+WbIGAgQREjl8sAYvGLTTBuMGAl20QwZTBgJdshMGAXGxAQGwCitYIdgb9FmyCgEGERI5MDEBIwMjEzMDMwEhAQEhAdh+S+287UteAW0BNv4fATT+3QGs/lQEOv5QAbD97v3YAAAB/7///wRJBDoAEABPsgQREhESOQCwAEVYsAAvG7EAHD5ZsABFWLABLxuxARA+WbAARViwCC8bsQgQPlmwABCxAwGwCitYIdgb9FmwCBCxCgGwCitYIdgb9FkwMQEDIxMhAwYGJyM3NzY2NzcTBEm77pr+2mM1zJ9SFiRbcx8PYAQ6+8YDdv485s0ByQMIl69SAc4AAQAiAAAFmgQ6AAwAWQCwAEVYsAEvG7EBHD5ZsABFWLALLxuxCxw+WbAARViwAy8bsQMQPlmwAEVYsAYvG7EGED5ZsABFWLAJLxuxCRA+WbIACwMREjmyBQsDERI5sggLAxESOTAxAQEhAyMTASMDAyMTIQKvAb0BLrztev5soqaA7bwBJQEtAw37xgK6/UYC2v0mBDoAAQAZAAAERwQ6AAsAhgCwAEVYsAYvG7EGHD5ZsABFWLAKLxuxChw+WbAARViwAC8bsQAQPlmwAEVYsAQvG7EEED5ZsgkKABESObAJL7S/Cc8JAl20XwlvCQJxtO8J/wkCcbJfCQFytC8JPwkCcrK/CQFxtB8JLwkCcbKPCQFdtI8JnwkCcrECAbAKK1gh2Bv0WTAxISMTIQMjEzMDIRMzA4vuTP5qTO687k8Bl07uAbX+SwQ6/j0BwwAAAQAZAAAESAQ6AAcAOQCwAEVYsAYvG7EGHD5ZsABFWLAALxuxABA+WbAARViwBC8bsQQQPlmwBhCxAgGwCitYIdgb9FkwMSEjEyEDIxMhA4zumv5pmu68A3MDdvyKBDoAAQBTAAAECAQ6AAcAMgCwAEVYsAYvG7EGHD5ZsABFWLACLxuxAhA+WbAGELEAAbAKK1gh2Bv0WbAE0LAF0DAxASEDIxMhNyED5v6sm+2a/q8iA5MDefyHA3nBAAADAD3+YAVRBgAAIQAsADgAfrITOToREjmwExCwKdCwExCwNNAAsAMvsABFWLAALxuxABw+WbAARViwBy8bsQccPlmwAEVYsBQvG7EUEj5ZsABFWLAYLxuxGBA+WbAARViwES8bsREQPlmwABCxNgGwCitYIdgb9FmwJtCwGBCxMQGwCitYIdgb9FmwK9AwMQEWFxMzAzYXFhYHBgcHDgInJicDIxMGIyImJyY3NzYSNgE2JyYnJgcDFjMyAQYXFhcWNxMmIyYDAhhERVjtWkZImJ8BAQYFF4S8cU9IUu1SPkaSoQMBBgYagb8CuQkBBZAjMYMnJub9BAkDCogYN4QkIdc7BFACHQHP/i0hAgLx0UA4JKPwcgMBIP5VAacZ2bg8Nyu0AQR+/cJbOdkHAgz9NwsBR1cwtAcBCALMCwT+mQAAAQAZ/r8ESAQ6AAsAPACwCC+wAEVYsAAvG7EAHD5ZsABFWLAELxuxBBw+WbAARViwCi8bsQoQPlmxAgGwCitYIdgb9FmwBtAwMRMzAyETMwMzAyMTIdXumwGYmu6bkG3ZOPzqBDr8iAN4/Ij9/QFBAAEAcAAABCAEOwASAEmyDhMUERI5ALAARViwCC8bsQgcPlmwAEVYsBEvG7ERHD5ZsABFWLAALxuxABA+WbIOEQAREjl8sA4vGLEEAbAKK1gh2Bv0WTAxISMTBiMmJjcTMwMGFxYXFjcTMwNk7UZbYcLPEzXuNgYFDJJTcmHtAWsWAty8AUz+szAmeQYDFwINAAEAGQAABioEOgALAEkAsABFWLAALxuxABw+WbAARViwAy8bsQMcPlmwAEVYsAcvG7EHHD5ZsABFWLAJLxuxCRA+WbEBAbAKK1gh2Bv0WbAF0LAG0DAxAQMhEzMDIRMzAyETAcObAUab7ZoBR5rtvPqrvAQ6/IgDePyIA3j7xgQ6AAABABL+vwY8BDoADwBMALAML7AARViwAC8bsQAcPlmwAEVYsAMvG7EDHD5ZsABFWLAHLxuxBxw+WbAARViwDS8bsQ0QPlmxAQGwCitYIdgb9FmwBdCwCdAwMQEDIRMzAyETMwMzAyMTIRMBu5sBR5rtmgFHm+yatG3ZOfrjuwQ6/IgDePyIA3j8iP39AUEEOgACAE8AAASmBDoADAAVAGGyDBYXERI5sAwQsA3QALAARViwCy8bsQscPlmwAEVYsAcvG7EHED5ZsgELBxESObABL7ALELEJAbAKK1gh2Bv0WbAHELEPAbAKK1gh2Bv0WbABELEVAbAKK1gh2Bv0WTAxARcWFgcGBCMhEyE3IQMDFzY2NzYmJwJR1rDPCQv+/Mv+IZr+0SICHF092Fx8DQtMTALiAQTCoanRA3bE/eX+owECXlNNWQQAAwAiAAAF8QQ6AAoAEwAXAG+yAhgZERI5sAIQsBHQsAIQsBXQALAARViwCS8bsQkcPlmwAEVYsBYvG7EWHD5ZsABFWLAHLxuxBxA+WbAARViwFC8bsRQQPlmyAQcJERI5sAEvsQsBsAorWCHYG/RZsAcQsQ0BsAorWCHYG/RZMDEBFxYWBwYEIyETMwMDFzY2NzYmJwEjEzMBj9awzwkL/vzL/iG87V092Fx8DQtNSwLU7bztAuIBBMKhqdEEOv3l/qMBAl5TTVkE/eIEOgACACIAAAPkBDoACgATAE+yDRQVERI5sA0QsAfQALAARViwCS8bsQkcPlmwAEVYsAcvG7EHED5ZsgEHCRESObABL7ELAbAKK1gh2Bv0WbAHELENAbAKK1gh2Bv0WTAxARcWFgcGBCMhEzMDAxc2Njc2JicBj9awzwkL/vzL/iG87V092Fx8DQtNSwLiAQTCoanRBDr95f6jAQJeU01ZBAAAAQAj/+gD1ARQAB8Ad7IAICEREjkAsABFWLAILxuxCBw+WbAARViwES8bsREQPlmwCBCxAAGwCitYIdgb9FmyHAgRERI5fLAcLxiyUxwBXbJAHAFdsgMcABESObEbB7AKK1gh2Bv0WbARELEYAbAKK1gh2Bv0WbIVGxgREjmyUxUBXTAxASYGBwc+AhceAgcHBgIGJyYmNxcGFhcWEwU3ITYmAixUfRDfCYPOcoi9Vw8DEpbujqvQBt8FV1HHXP6uHgFDCF0DjAJpUQFssGEBBIz4lhuf/v6NBATgswFbdgQGASoBqH6TAAIAJP/pBhAEUwAXACcAjrImKCkREjmwJhCwD9AAsABFWLAWLxuxFhw+WbAARViwBC8bsQQcPlmwAEVYsBQvG7EUED5ZsABFWLAOLxuxDhA+WbIAFhQREjmwAC+0HwAvAAJxsr8AAXGyjwABXbJfAAFysRMBsAorWCHYG/RZsA4QsR0BsAorWCHYG/RZsAQQsSQBsAorWCHYG/RZMDEBMzYkFx4CBwcGAgcGJy4CNwcDIxMzAQYXFhYXFjY3NzQmJyYGBwGBu0cBIcCLxF0QAha0jWR6fsVjCMtP7bztAU0GAwNiWneqGQdhYHmnGQKH2/EEBIz9mBau/u8/LQMDfdeCAf48BDr90Tc8aYADBcGsYYSPBAPBrwAC/7YAAAQWBDsADQAWAGOyFBcYERI5sBQQsATQALAARViwAC8bsQAcPlmwAEVYsAEvG7EBED5ZsABFWLAFLxuxBRA+WbISAAEREjmwEi+xAwGwCitYIdgb9FmyBwMSERI5sAAQsRMBsAorWCHYG/RZMDEBAyMTIwEhASYmNzYkMwMGFhcXEycGBgQWvOxF0/7a/vwBTlBNBQoBCMXrC05E8zbLXH8EOvvGAY3+cwG6LZZbocL+l0BOAgEBOAECXwABAA3+RwP5BgAAIwCIsgMkJRESOQCwIS+wAEVYsAQvG7EEHD5ZsABFWLALLxuxCxI+WbAARViwGi8bsRoQPlm2nyGvIb8hA12yLyEBXbIPIQFdsiMaIRESObAjL7Af0LEcB7AKK1gh2Bv0WbAB0LICGgQREjmwCxCxEAGwCitYIdgb9FmwBBCxFwGwCitYIdgb9FkwMQEhBzYXFhYHAwYGIyYnNxYzMjcTNicmJyYHAyMTIzczNzMHIQLM/v4zh6uZlxN6GMmlQ0IfNSt/IHwFBA2DhWaH7c+ZHpkd7h4BBASt6o4EAtPA/Qm1xQIQwRDCAu8rJXoDAoT8+gStq6ioAAABADv/6AP2BFQAHwBlshggIRESOQCwAEVYsBAvG7EQHD5ZsABFWLAILxuxCBA+WbEAAbAKK1gh2Bv0WbIaEAgREjl8sBovGLEcB7AKK1gh2Bv0WbIDABwREjmwEBCxFwGwCitYIdgb9FmyFBoXERI5MDElFjY3Nw4CJy4CNzcSABcWFgcjNCYnJgYHJQchBhYB5VaDFN8LhNVxjL9WEAIdATDesM4C3VxTaJMtAVge/rcNX60CZ1MBa69kAwSK95gUAQIBNgYE4bRhcgQDjJoBqICTAAL/vQAABkYEOgAXAB8AfbIKICEREjmwChCwGdAAsABFWLAALxuxABw+WbAARViwCC8bsQgQPlmwAEVYsA8vG7EPED5ZsgIACBESObACL7AAELEKAbAKK1gh2Bv0WbAPELERAbAKK1gh2Bv0WbAIELEaAbAKK1gh2Bv0WbACELEfAbAKK1gh2Bv0WTAxAQMXFhYHBgQjIRMhAwIGByM3NzY2NzcTAQMXNjY3NicEMEHWss8JC/8AzP4hmv7xSzfJpmQVJVxvHhJgAns32Fl9DRKjBDr+hwEFt5mlxgN2/qv+1fEFyQMIlp1lAc79xf7BAQJcT4gKAAIAGQAABlwEOgASABsAhbIBHB0REjmwARCwE9AAsABFWLACLxuxAhw+WbAARViwES8bsREcPlmwAEVYsAsvG7ELED5ZsABFWLAPLxuxDxA+WbIBEQsREjmwAS+yBBELERI5sAQvsAEQsQ0BsAorWCHYG/RZsAQQsRMBsAorWCHYG/RZsAsQsRQBsAorWCHYG/RZMDEBIRMzAxcWFgcGBCMhEyEDIxMzAQMXNjY3NiYnAXsBl0fuQtayzwkJ/v/N/iFT/mpT7rzuAiE42F17CwpKUQKfAZv+hwEFt5mkxwHd/iMEOv3F/sEBAl9MQE0FAAEADQAAA/kGAAAaAHWyAxscERI5ALAYL7AARViwBC8bsQQcPlmwAEVYsBEvG7ERED5ZsABFWLAJLxuxCRA+WbK/GAFdsi8YAV2yDxgBXbIaERgREjmwGi+wFtCxEwewCitYIdgb9FmwAdCyAgQRERI5sAQQsQ4BsAorWCHYG/RZMDEBIQc2FxYWBwMjEzYnJicmBwMjEyM3MzczByEC4f7kLoesmpUTdO12BQMNg4Roh+3Qhx6HHO4fARkEtfKOBALWvf1IArsrJXoDAoT8+gS1qqGhAAABABn+mgRIBDoACwBGALAIL7AARViwAC8bsQAcPlmwAEVYsAMvG7EDHD5ZsABFWLAFLxuxBRA+WbAARViwCS8bsQkQPlmxAQGwCitYIdgb9FkwMQEDIRMzAyEDIxMhEwHDmwGYmu68/r8+7j/+u7wEOvyIA3j7xv6aAWYEOgABAGD/5gcuBbAAIwBhsgYkJRESOQCwAEVYsAAvG7EAID5ZsABFWLANLxuxDSA+WbAARViwGC8bsRggPlmwAEVYsAQvG7EEED5ZsABFWLAJLxuxCRA+WbIHAAQREjmxFAGwCitYIdgb9FmwH9AwMQEDBgQnJiYnBicmJjcTMwMGFxYWFxY2NxMzAwYXFhYXFjY3Ewcurx3+785soCWO2rvPFa73rwUDBUtDZIkUr/uvBQUHUEVfgRWvBbD7/dD3BAJXTKkEBPrEBAT7+yorSFcDBIN4BAX7+y0rS1EDA397BAUAAQBE/+YGHgQ6ACIAXbIXIyQREjkAsABFWLAALxuxABw+WbAARViwDS8bsQ0cPlmwAEVYsBcvG7EXHD5ZsABFWLAJLxuxCRA+WbAE0LAEL7IHFwkREjmwCRCxEwGwCitYIdgb9FmwHtAwMQEDBgYnJiYnBicmJjcTMwMHFBYXFjY3EzMDBhcWFhcWNjcTBh5zHPK3W44igrqpshNz7XIEODhTdBNz7nIEAgJCO09oEHMEOv1SxOIEAkpCkQQE5rYCr/1QR0NRAwVzcAKw/VAmJkNOAQN2awKwAAACACH//APpBhgAEgAbAHSyFRwdERI5sBUQsAPQALAARViwDy8bsQ8iPlmwAEVYsAkvG7EJED5ZshIPCRESObASL7EAB7AKK1gh2Bv0WbICDwkREjmwAi+wABCwC9CwEhCwDdCwAhCxEwGwCitYIdgb9FmwCRCxFAGwCitYIdgb9FkwMQEhAxcWFgcGBCchEyM3MxMzAyEBAxc2Njc2JicC4/7nNse51QwN/vTC/h+8qR6oNu02ARr+ckPZYHwLCkZPBDr+yQEBzKm22gQEOqsBM/7N/Vv+ggICcFZMZgUAAAEAK//pBt8FygAmAIyyHCcoERI5ALAARViwJS8bsSUgPlmwAEVYsAQvG7EEID5ZsABFWLAjLxuxIxA+WbAARViwGy8bsRsQPlmyACUjERI5sAAvsgcEGxESObAEELELAbAKK1gh2Bv0WbAAELAO0LAAELEiAbAKK1gh2Bv0WbAR0LAbELEVAbAKK1gh2Bv0WbIYGwQREjkwMQEXEgAXFhIXIyYmJyYGByUHIQcGFhcEEzcGACcuAicmNwcDIxMzAbSmUAFd+tj7C/UFeXeV0jwB4iL+KwoNfX8BF0/2J/6u8InReAYEDrVx9vz3A08BATIBSgUE/vrsnIsDBc/hAcNkqsIECwEtAeT+8gQDfuqSUVIB/XQFsAABABn/6AWkBFMAJgCYsg0nKBESOQCwAEVYsCYvG7EmHD5ZsABFWLAELxuxBBw+WbAARViwIy8bsSMQPlmwAEVYsB4vG7EeED5Zsg4eBBESOXywDi8YslIOAV2yQA4BXbAB0LAEELELAbAKK1gh2Bv0WbIIDgsREjmwDhCxDwewCitYIdgb9FmwHhCxFgGwCitYIdgb9FmyGRYPERI5sA8QsCHQMDEBMzYkFxYWByM0JicmAyUHIQYXFhcWFxY2NzcOAicmAjcHAyMTMwFzjkUBHMOv0ALdWVbRVgF5Hv6WBQULSiU6WIET4AuI03DF4RKhTu687gJx7fUFBOC1X3QEBv7eAasyMmwwGAECaVEBbLBiAwQBEccB/joEOgAAAv+uAAAEhAWwAAsADgBXALAARViwCC8bsQggPlmwAEVYsAIvG7ECED5ZsABFWLAGLxuxBhA+WbAARViwCi8bsQoQPlmyDQgCERI5sA0vsQABsAorWCHYG/RZsATQsg4IAhESOTAxASMDIxMjAyEBMxMjASEDA059StxKadX+9wLz7/T2/lwBSEsBqv5WAar+VgWw+lACaAH1AAL/nAAAA7gEOgALABAAVwCwAEVYsAgvG7EIHD5ZsABFWLACLxuxAhA+WbAARViwBi8bsQYQPlmwAEVYsAovG7EKED5Zsg0CCBESObANL7EBAbAKK1gh2Bv0WbAE0LIPCAIREjkwMQEjAyMTIwMjATMTIwEzAycHAp9jML4xUpb7Aljh4+L+s/A2BS4BF/7pARf+6QQ6+8YBxAETVG0AAgA+AAAGjQWwABMAFgB9ALAARViwAi8bsQIgPlmwAEVYsBIvG7ESID5ZsABFWLAELxuxBBA+WbAARViwCC8bsQgQPlmwAEVYsAwvG7EMED5ZsABFWLAQLxuxEBA+WbIVAgQREjmwFS+wANCwFRCxBgGwCitYIdgb9FmwCtCwBhCwDtCyFgIEERI5MDEBIQEzEyMDIwMjEyMDIRMhAyMTMwEhAwGfAVgBsvD09kB9St1KaNX+9t7+60v2/fYBwgFITAJnA0n6UAGq/lYBqv5WAav+VQWw/LgB9gACADAAAAV9BDoAEwAYAIAAsABFWLACLxuxAhw+WbAARViwEi8bsRIcPlmwAEVYsAQvG7EEED5ZsABFWLAILxuxCBA+WbAARViwDC8bsQwQPlmwAEVYsBAvG7EQED5ZsgAQEhESObAAL7AB0LEOAbAKK1gh2Bv0WbAL0LAH0LABELAU0LAV0LIXEgQREjkwMQEzATMTIwMjAyMTIwMjEyMDIxMzATMDJwcBa/ABXuHj5zZdMr4xUpb7m64x7bvuAXXwNgUuAcQCdvvGARf+6QEX/ukBF/7pBDr9igETVG0AAAIAFAAABmQFsAAbAB4AebIMHyAREjmwDBCwHNAAsABFWLAaLxuxGiA+WbAARViwBC8bsQQQPlmwAEVYsAwvG7EMED5ZsABFWLATLxuxExA+WbIYGgQREjmwGC+wANCwGBCxDwGwCitYIdgb9FmwCdCyHBoEERI5sBoQsR0BsAorWCHYG/RZMDEBFhYHAyMTNiYnJwcDIxMnJgYHAyMTNiQlFwMhAQEhBHnZ1Bc59jkQVnxoDGz2aWyFnxY69jkgARsBARH2BMD9JAEs/j4DJATv0f6gAWF5fQUDD/2wAlwCAXOG/poBYOTjAgECiP2MAacAAgAWAAAFKgQ6ABsAHgB1shwfIBESObAcELAU0ACwAEVYsAUvG7EFHD5ZsABFWLAALxuxABA+WbAARViwCy8bsQsQPlmwAEVYsBQvG7EUED5ZsATQsAQvsAfQsAQQsRIBsAorWCHYG/RZsBfQshwFABESObAFELEdAbAKK1gh2Bv0WTAxMzc2NjcDIQEWFgcHIzc2JyYnJwcDIxMnIgYHBwETIRYaHefQsQPX/pSknxQZ7hoGAQaaJAZN7E4mcoQVHAHdwv7gr8zXDgHa/iAQ476pqjQtjQ0CCP5hAaYBc362AmsBIAACADUAAAiZBbAAIQAkAJmyHSUmERI5sB0QsCTQALAARViwBy8bsQcgPlmwAEVYsAsvG7ELID5ZsABFWLAALxuxABA+WbAARViwBS8bsQUQPlmwAEVYsBEvG7ERED5ZsABFWLAaLxuxGhA+WbIJBwAREjmwCS+xHQGwCitYIdgb9FmwA9CwCRCwDdCwHRCwF9CyIgcAERI5sAsQsSMBsAorWCHYG/RZMDEhEzY3BQMjEzMDIQMhARYWBwMjEzYnJicnBwMjEycmBgcDAQEhAkc7F1b+p2v2/fZwAx3+BML+E9nUFzn2OgcGErJnC2z2aW6EnxY7AoABK/49AV+fawP9mgWw/XsChf10BO/R/qABYT0uigYDDf2uAlwCAXOG/poDOgGpAAACACIAAActBDoAIQAkAJuyGyUmERI5sBsQsCTQALAARViwBy8bsQccPlmwAEVYsAsvG7ELHD5ZsABFWLAALxuxABA+WbAARViwBS8bsQUQPlmwAEVYsBEvG7ERED5ZsABFWLAaLxuxGhA+WbAFELAJ0LAJL7AK0LEcAbAKK1gh2Bv0WbAE0LAKELAN0LAcELAX0LIiCwAREjmwCxCxIwGwCitYIdgb9FkwMSE3NjcFAyMTMwMhAyEBFhYHByM3NicmJycHAyMTIwYGBwcBEyECGBwaTf6+Su287VIClrkD1/6VoaAUGe0aBwIHmiMGTexOK3OBFBoB3cL+4KmeZAP+WAQ6/icB2f4gEOK/qao1LJEJAgj+YQGmAXaFqgJrASAAAAL/qv5CBDEHjAAqADMAjrIJNDUREjmwCRCwM9AAsBsvsDAvsABFWLAJLxuxCSA+WbAARViwFS8bsRUQPlmyAAkVERI5sAAvsAkQsQYBsAorWCHYG/RZsAAQsSgBsAorWCHYG/RZsg8oABESObAVELEiAbAKK1gh2Bv0WbIPMAFdsDAQsDLQsDIvsg8yAV2yKzAyERI5sC3QsC0vMDEBMjY3NiYnJTcXHgIHBgUWFgcGBCcnBgcGFwcmJjc2NjMXMjY3NiYnJzcBNzcVASMDNRcBpH2kDgtla/7eI/iH0moIEf72ZmgHD/7V2zWMERCHW3SFBgXGqjRyqQ8OeICZIwGUqtD+zZPpxANNc2pWYwUBxwEBXKl04W0sq3DJ7wIBBWloPpUquXGElwGBbGt5BQHHA6CZBBD+7AEVEAQAAAL/tf5KA8UGIAAlAC4AwrIrLzAREjmwKxCwBNAAsCsvsABFWLAHLxuxBxw+WbAARViwFy8bsRcSPlmwAEVYsBEvG7ERED5ZsgARBxESObAAL7S/AM8AAl20XwBvAAJxtC8APwACcrTvAP8AAnG0HwAvAAJxso8AAV2yvwABcrAHELEEAbAKK1gh2Bv0WbAAELEjB7AKK1gh2Bv0WbIMIwAREjmwERCxHQGwCitYIdgb9FmwKxCwLdCwLS+0Dy0fLQJdsiYrLRESObAo0LAoLzAxATY3NichNxcWFgcGBxYHBgQjIwYHBhcHJiY3NjYzFzI2NzYnIzcBNzcXASMDNRcBhOQXEsL+3iHvzukHCtGsBAX+89YlkxEQf1loggQFv6EwaI0NFOahHgFPqtAB/syT6cMCbgaRdQe5AQGajZ1cRpqerwVqYUKPLrFtf48BUEaGB6kDE5kEEf7tARQRBAADAGL/5wUaBcgAEgAbACQAc7IUJSYREjmwFBCwCdCwFBCwHdAAsABFWLAKLxuxCiA+WbAARViwAC8bsQAQPlmwChCxEwGwCitYIdgb9FmyFgoAERI5fLAWLxiycxYBXbJgFgFdsAAQsRwBsAorWCHYG/RZsBYQsSAHsAorWCHYG/RZMDEFLgInJhI3NiQXFgAXFgICBwYDJgYHJTY3NiYBFjY3BQYVFBYCUI/WeggHOEVgATO92AEBDAY5i2eyGpnaPgKoBwEDhP68mtU+/VgGhhQDg/idcwFDh7vJBAT+3vd8/r/+81qcBQwF3vIBMDWnuvvMBdvvATAzp7YAAAMANv/nBCYEUgARABcAHQBtshgeHxESObAYELAM0LAYELAS0ACwAEVYsAQvG7EEHD5ZsABFWLANLxuxDRA+WbESAbAKK1gh2Bv0WbIaBA0REjl8sBovGLJSGgFdskAaAV2xFQewCitYIdgb9FmwBBCxGAGwCitYIdgb9FkwMRM2EjYXHgIHBwYCBicmAjc3ARYTBQYWEyYDJTYmRhKb85OLx1sQAhSc85LI4QoDAafSYf4OCGXlzWQB8QhoAiCeAQWPBASO/JYWn/7+jAQFARnaKP6iBwEkAYOWAtwH/uABfZgAAAEAqAAABV4FxgAPAEeyAhARERI5ALAARViwBi8bsQYgPlmwAEVYsA8vG7EPID5ZsABFWLAMLxuxDBA+WbIBDA8REjmwBhCxCAGwCitYIdgb9FkwMQEXNwE2NhcXByciBwEjAzMCKgQyAVdLtHYyGRFbPv3i7uf+AYBjdgLtspQCAdcBgfuUBbAAAAEAdwAABEQEUgAQAEeyDRESERI5ALAARViwBS8bsQUcPlmwAEVYsBAvG7EQHD5ZsABFWLANLxuxDRA+WbIBDRAREjmwBRCxCgGwCitYIdgb9FkwMQEXNxMSMzIXByYHIgcBIwM3AakCJL93zzg4JxgSSzf+e86n5wFuYGABwgEiGMEKAm/87gQ6AQAAAgBm/3UFFAYvABQAJgBXshknKBESObAZELAA0ACwAEVYsA0vG7ENID5ZsABFWLADLxuxAxA+WbAA0LANELAK0LANELEXAbAKK1gh2Bv0WbAa0LADELEgAbAKK1gh2Bv0WbAj0DAxBQcnNyYCJzcSACU3FwcWEhcWBwIAEyYnByc3BgIDFRYXNxcHJBM2AqQcwRyxyAQBEgFNARAZwRmvxwUCHDT+xpUFnBXCFqWyDwyYFcIWAQ8+GAx/AYAkAR7iTAFuAcMmcgF0JP7h5niW/uf+qgOh8EBiAWQ1/rL+xULjPWIBYlcBlLYAAgA4/4cENQS1ABMAIwBasgAkJRESObAU0ACwAEVYsAAvG7EAHD5ZsABFWLAKLxuxChA+WbAAELAD0LAKELAN0LAKELEUAbAKK1gh2Bv0WbAAELEcAbAKK1gh2Bv0WbAZ0LAUELAh0DAxATcXBxYSBwcGAAcHJzcmAjc3NhITNjY3NicHJzcGBgcGFzcXAjUZtBmmphUCHP76yBi0GKWjFQcj/9RvfQYEbhW0Fm15BwdsF7QERm8Bbyf+288W4P7bHGwBbicBI8sx2gES/JIt7LO4PGEBYzDnsbY/aQEAAwBi/+UG3AdEADEARgBPALOyPVBRERI5sD0QsAnQsD0QsEfQALAARViwFC8bsRQgPlmwAEVYsAcvG7EHED5ZsBQQsADQsAAvsgoHFBESObAHELAM0LAUELEVAbAKK1gh2Bv0WbAHELEpAbAKK1gh2Bv0WbAe0LIiFAcREjmwFRCwMdCwFBCwPtCwPi+wM9CwMy+xMgiwCitYIdgb9FmwMxCwOdCwOS+xQgiwCitYIdgb9FmwPhCwS9CwSy+wT9CwTy8wMQEWEgcDBgAnJiYnBicuAjcTNiQ3BwYGBwMGFxYWFxY2NxMzAwYXFhYXFjY3EzY1JicTByMuAyMiBgcHJzc2NhceAwE2Njc3FwcGBwVYvccXVR7+78lnoymS0HyzUg9VHwER1RdhgBVVBQECSURmiRQ/7z8FBQhVR158FlYGBIqxCR47cXFtNzNACQKDAgiCbDBatWL97SsnCBKlDRGeBbEJ/vvN/e3c/v8EAlNJowYCedqDAhPe+gTMAoyC/ewqLlNfBAWGewF//nwvLElRAwOKiAIVLS6mCgHmiAInLyQ4MRMBJmxxAgEXSRn+ijE+JV4BZm9bAAMAS//lBcMF6AAwAEUATQCzsjpOTxESObA6ELAK0LA6ELBG0ACwAEVYsBUvG7EVHD5ZsABFWLANLxuxDRA+WbAVELAA0LAAL7ANELAI0LILDRUREjmwFRCxFgGwCitYIdgb9FmwDRCxHQGwCitYIdgb9FmyIRUNERI5sCjQsBYQsDDQsBUQsD3QsD0vsDLQsDIvsTEIsAorWCHYG/RZsDIQsDjQsDgvsUEIsAorWCHYG/RZsD0QsEnQsEkvsE3QsE0vMDEBHgIHBwYGJyYmJwYnJiY3EzY3NjcHBg8CBhYXFjY3NzMHBhcWFhcWNjcTNzYmJwEHIy4DIyIGBwcnNzY2Fx4DATY3NxcHBgcEa3GeSQ0hHeyyWY0jgLCorhQkIYx3rxWpJyQEBDc2UG8RH+YdBAMDRTtHYhEmBAI7OgEDCSE6bXhrNzJACQKEAgiCbDBav1n98EsPEaYNEKAESAZvxHzu0+0FAktElAQE8b4BA9hvXgPDB+X9SEhfAgV3bMfHJiZCUAEDenUBDD9FVQYB6ogCJTIjODETASZscQIBF00V/ohVP14BZW9cAAACAGD/5ActBxEAIwArAIeyBiwtERI5sAYQsCrQALAARViwAC8bsQAgPlmwAEVYsA0vG7ENID5ZsABFWLAYLxuxGCA+WbAARViwCS8bsQkQPlmwBNCwBC+yBwAJERI5sAkQsRQBsAorWCHYG/RZsB/QsAAQsCrQsCovsCjQsCgvsSYIsAorWCHYG/RZsCgQsCvQsCsvMDEBAwYEJyYmJwYnJiY3EzMDBhcWFhcWNjcTMwMGFxYWFxY2NxMlNyEHIQcjNwctrx3+7s1poiaP2b/KFK73rwUDBUtDZIkUr/uvBQUHUEVdgxWv/H0WAz0V/rEXsRcFsPv90PkEAldOqgQG+8IEBPv7KitKVQMEg3gEBfv7LStLUQMDfnwEBed6en9/AAIARP/mBh4FsQAiACoAi7IXKywREjmwFxCwKdAAsABFWLAALxuxABw+WbAARViwDS8bsQ0cPlmwAEVYsBcvG7EXHD5ZsABFWLAELxuxBBA+WbAARViwCS8bsQkQPlmyBxcEERI5sRMBsAorWCHYG/RZsB7QsBcQsCnQsCkvsCrQsCovsSQIsAorWCHYG/RZsCoQsCfQsCcvMDEBAwYGJyYmJwYnJiY3EzMDBxQWFxY2NxMzAwYXFhYXFjY3EyU3IQchByM3Bh5zHPO2W44ig7mpshNz7XIEODhTcxN07nIEAgJCO09oEHP84hYDIRP+vhexFgQ6/VLG4AQCSkKSBATptAKv/VBHQ1EDA3BrArb9UCYmQ04BA3ZrArD8e3t/fwAAAQBW/owE6gXKABkAVbIAGhsREjkAsABFWLAKLxuxCiA+WbAARViwAC8bsQAYPlmwAEVYsAIvG7ECED5ZsAoQsA7QsAoQsRABsAorWCHYG/RZsAIQsRgBsAorWCHYG/RZMDEBIxMmJgI3ExIAFxYSBycSJyYGBwMHBhYXFwJ69UV9rUoTKi0BXfLk9wz2EviPyyAtAwN0aqf+jAFoGqkBApIBDAEfAVQFBP735gEBIAcD4sj+4UCRqQQBAAABAEX+iQP8BFMAGQBVsgAaGxESOQCwAEVYsAovG7EKHD5ZsABFWLAALxuxABg+WbAARViwAi8bsQIQPlmwChCwDtCwChCxEQGwCitYIdgb9FmwAhCxGAGwCitYIdgb9FkwMQEjEyYCNzcSNzYXFhYHJzYmJyYGBwcGFhcXAiTtRZucFgEdmZnWrM8G3wVWUnGjFgoHVlid/okBbCcBIMwLAQaenAUE47IBW3cEBcKjanyTBAIAAAEAOAAABLoFPgATABMAsA4vsABFWLAELxuxBBA+WTAxARcHJwMjASc3FwEnNxcTNwEFBycCMPtU/em5ASb7VP4BC/1W/e23/tUBAFn5Abisdar+vwGXq3WrAXOrd6sBRwH+Yqt0qQAAAfzmBKL/4gX9AAcAEgCwAC+xAwawCitYIdgb9FkwMQEHJzchNxcH/aoWrisCEROtJwUgfgHubAHcAAH9DgUW//MGFAASAC0AsAQvsAjQsAgvsQACsAorWCHYG/RZsAQQsA3QsA0vsQ4CsAorWCHYG/RZMDEDFhYHByc3NicmBgQHBzcyPgLkZHMEA4ICBlYqU/7zQUMLSlfRYQYTAmxnKAEUXQQCEGIFAYcTTRcAAf4rBRX/AgZgAAUADACwAS+wBdCwBS8wMQE3MwcXB/4rFrkeJlAF53mkbDsAAAH+PAUX/1sGYAAFAAwAsAMvsADQsAAvMDEBJzc3Mwf+ik5PF7kZBRdOcomPAAAI+kH+wgGeBbEACwAXACMALwA7AEcAUwBfAH8AsD8vsEsvsFcvsDMvsABFWLADLxuxAyA+WbEJCbAKK1gh2Bv0WbA/ELAP0LA/ELFFCbAKK1gh2Bv0WbAV0LBLELAb0LBLELFRCbAKK1gh2Bv0WbAh0LBXELAn0LBXELFdCbAKK1gh2Bv0WbAt0LAzELE5CbAKK1gh2Bv0WTAxATY2FzIWFSc2IyYHATY2MxYWFyc2IyIHAzY2FxYWFyc2IyYHATY2FxYWFyc2IyYHATY2FxYWFyc2IyYHATY2FzIWFSc2IyIHATY2FxYWFyc2IyYHAzY2FxYWFyc2IyYH/Z0Ib1tXbWsFUFUbAZ0Ib1pZawJsBVBSHRIIbltYagJrBVBTHv56CHFXWGoCawVQUh79MAhwW1hqAmsFUFMe/kIIcFtXbWsFT1Qd/o8IbltYagJrBVBTHicIb1pYawJsBVBSHgTzWGYBaVYBZgJm/upXZgFmWAFmZP4HWGYBAWZXAWYCZv33WWYCAWZXAWYCZv7jWWUBAWdXAWYCZgUZWWUBaVYBZmT+B1hmAQFmVwFmAmb991hmAQFmVwFmAmYACPpv/mMBcwXGAAQACQAOABMAGAAdACIAJwAvALAhL7AWL7ASL7ALL7AbL7AmL7AARViwBy8bsQcgPlmwAEVYsAIvG7ECEj5ZMDEFFwMjExMnEzMDATcFByUFByU3BQE3JRcFAQcFJyUTJwM3EwEXEwcD/eQOq2Z9pA6qZn0BqQoBORD+wPuPCv7HEQE/A84DAUo//tD8ZgP+tkABMm0RX0GWArERX0OUOhP+sAFgBKERAVH+of4RCoBaRDwKgFpEAa4SmE6+/I0TmE+/AuQBAVM7/tD85gH+rj0BMAD//wAn/n4FfAclACYA3AAAACcAoQFKAT4BBwAQBFP/xgATALAARViwCC8bsQggPlmwDdwwMQD//wAZ/n4EdgXaACYA8AAAACcAoQCQ//MBBwAQA2L/xgATALAARViwCC8bsQgcPlmwDdwwMQAAAgAh//wD6QZiABIAGwB3shUcHRESObAVELAD0ACwAEVYsA0vG7ENID5ZsABFWLARLxuxESA+WbAARViwCS8bsQkQPlmwERCxAAewCitYIdgb9FmyAg0JERI5sAIvsAAQsAvQsAzQsAIQsRMBsAorWCHYG/RZsAkQsRQBsAorWCHYG/RZMDEBIQMXFhYHBgQnIRMjNzM3MwchAQMXNjY3NiYnAwb+51nHudUMDf70wv4f36keqCDtHwEZ/k9D2WB8CwpGTwUF/f4BAcypttoEBQWrsrL8kP6CAgJwVkxmBQACACcAAAUFBbAADgAbAE+yBBwdERI5sAQQsBfQALAARViwAy8bsQMgPlmwAEVYsAEvG7EBED5ZshYDARESObAWL7EAAbAKK1gh2Bv0WbADELEUAbAKK1gh2Bv0WTAxAQMjEwUyBAcGBxcHJwYjATY3NiYnJQMhNjcnNwF8Xvf9AffmAQQTE5RfcWeCqwEbLAsScW3+zFgBGUdOWHICHf3jBbAB+8zDgY1aljYBQ0RDbooEAf4EAheIWQAC/8f+YAQPBFIAFQAmAHCyBScoERI5sAUQsB/QALAARViwDi8bsQ4cPlmwAEVYsAsvG7ELHD5ZsABFWLAILxuxCBI+WbAARViwBS8bsQUQPlmyBw4FERI5sgwOBRESObAOELEZAbAKK1gh2Bv0WbAFELEeAbAKK1gh2Bv0WTAxJRcHJwYnJicDIwE3BzYXFhYXFgcHBgMmJicmBwMWFzI3JzcXNjc2A1RRcU5jZqViYe4BBNkSfKycsQYCBwUjwQJcVYViVS6EO0lRc0Q4EgqCgFl4NgICc/3+BdoBcIcEBNzEQD0k7wGDa34CBH/+HXgCIoNZaGFxSQAAAQAiAAAE3wcQAAkAM7IDCgsREjkAsABFWLAGLxuxBiA+WbAARViwBC8bsQQQPlmwBhCxAgGwCitYIdgb9FkwMQEjByEDIxMhEzMEjgcB/WzZ9/0CnT3mBO0J+xwFsAFgAAABABEAAAPMBXMABwAsALAARViwBC8bsQQcPlmwAEVYsAIvG7ECED5ZsAQQsQABsAorWCHYG/RZMDEBIQMjEyETMwN0/iWa7rwB3DfsA3b8igQ6ATkAAAEALv7EBKwFsAAXAF6yAxgZERI5ALAKL7AARViwFi8bsRYgPlmwAEVYsBQvG7EUED5ZsBYQsQABsAorWCHYG/RZsgMWFBESObADL7AKELELB7AKK1gh2Bv0WbADELESAbAKK1gh2Bv0WTAxASEDMxYWEgcCAAc3NhM2JyYmJyMDIxMhBIj9dUmYqe5rERv+zvwS70cgDQ2Gd7Rt9vwDggTk/l4Ej/79qf77/swGuwYBF4BxbnkE/YgFsAAAAQAR/t8DggQ6ABUATLIPFhcREjkAsAovsABFWLAULxuxFBw+WbAARViwEi8bsRIQPlmwFBCxAAGwCitYIdgb9FmyAxQSERI5sAMvsRABsAorWCHYG/RZMDEBIQcXHgIHBgIHJzY3NiYnJwMjEyEDX/46KECP2WkND/O0QuseDnV1XE/uvAK1A3blAQN51oij/vwws1HUeZEEAf46BDoA////pf6aB+AFsAAmANoAAAAHAlEGggAA////rf6aBnIEOgAmAO4AAAAHAlEFPAAA//8ALv6aBXsFsAAmAiwAAAAHAlEEEAAA//8AIv6aBIEEOgAmAPEAAAAHAlEDWQAAAAEAIwAABYMFsAAUAGIAsABFWLAALxuxACA+WbAARViwDC8bsQwgPlmwAEVYsAIvG7ECED5ZsABFWLAKLxuxChA+WbIPCgwREjmwDy+ynw8BXbEIAbAKK1gh2Bv0WbIBCA8REjmwBdCwDxCwEtAwMQkCIQMnByM3IwMjEzMDMzczAzMBBYP+CAEV/ta2QS6fKVVs9/33a1QtoDMyAX8FsP1O/QICbQHq6f2TBbD9mv7/AAJoAAEAIQAABM0EOgAUAF0AsABFWLANLxuxDRw+WbAARViwFC8bsRQcPlmwAEVYsAovG7EKED5ZsABFWLADLxuxAxA+WbIOCg0REjmwDi+xCQGwCitYIdgb9FmyAQkOERI5sAXQsA4QsBLQMDEBARMhAycHIzcjAyMTMwMzNzMHMwEEzf5q5f7ghi8kmCBTS+y87EtSJJgpIgEWBDr98f3VAawBs7L+VAQ6/lDHyQGyAAEApAAABuMFsAAOAGMAsABFWLAGLxuxBiA+WbAARViwCi8bsQogPlmwAEVYsAIvG7ECED5ZsABFWLANLxuxDRA+WbIIBgIREjmwCC+xAQGwCitYIdgb9FmwBhCxBAGwCitYIdgb9FmyDAEIERI5MDEBIwMjEyE3IQMzASEBASEDpq9s9tr+NSMCwGp9AgsBPv2XAYb+6AJw/ZAE7MT9nAJk/Tv9FQABAGwAAAW7BDoADgBtALAARViwBi8bsQYcPlmwAEVYsAovG7EKHD5ZsABFWLACLxuxAhA+WbAARViwDS8bsQ0QPlmyCQoCERI5sAkvsi8JAXGyjAkBXbEAAbAKK1gh2Bv0WbAGELEEAbAKK1gh2Bv0WbIMAAkREjkwMQEjAyMTITchAzMBIQEBIQMRfkrtmv52IgJ3TF8BbQE2/h4BNP7eAaz+VAN2xP5QAbD97f3Z//8AJ/6aBYcFsAAmACwAAAAHAlEEaQAA//8AGf6aBGkEOgAmAPQAAAAHAlEDawAAAAEAJwAAB98FsAANAF8AsABFWLACLxuxAiA+WbAARViwDC8bsQwgPlmwAEVYsAYvG7EGED5ZsABFWLAKLxuxChA+WbIBAgYREjmwAS+wAhCxBAGwCitYIdgb9FmwARCxCAGwCitYIdgb9FkwMQEhEyEHIQMjEyEDIxMzAbECdmkDTyL9qNv2cP2KcPf99wNSAl7D+xMCh/15BbAAAAEAEQAABZIEOgANAGgAsABFWLACLxuxAhw+WbAARViwDC8bsQwcPlmwAEVYsAYvG7EGED5ZsABFWLAKLxuxChA+WbIBDAYREjl8sAEvGLRAAVABAl2wAhCxBAGwCitYIdgb9FmwARCxCAGwCitYIdgb9FkwMQEhEyEHIQMjEyEDIxMzAWwBl04CQSP+rprtTP5pTO687gJ3AcPE/IoBtf5LBDoAAQAu/sIHhgWwABkAa7IUGhsREjkAsAgvsABFWLAYLxuxGCA+WbAARViwEi8bsRIQPlmwAEVYsBYvG7EWED5ZsgEYEhESObABL7AIELEJB7AKK1gh2Bv0WbABELEQAbAKK1gh2Bv0WbAYELEUAbAKK1gh2Bv0WTAxATMWFhIHAgAHNzYTNicmJicjAyMTIQMjEyEFFm6p7msRG/7O/BLvRyANDYZ3im322f2U2fb8BFkDQASP/v2p/vv+zAa7BgEXgHFueQT9igTk+xwFsAABABH+4wZSBDoAFwBZshAYGRESOQCwBy+wAEVYsBYvG7EWHD5ZsABFWLAQLxuxEBA+WbAARViwFC8bsRQQPlmyARYQERI5sAEvsQ4BsAorWCHYG/RZsBYQsRIBsAorWCHYG/RZMDEBFxYABwYCByc2Njc2JicnAyMTIQMjEyED9mHuAQ0TD/SzQnmEDA9/f41Q7Zn+aZruvANzApQBAv781Kb/ADCyKphjeJMEAf42A3b8igQ6AAIAZf/oBdkFxwArADoAkLIZOzwREjmwGRCwOtAAsABFWLAgLxuxICA+WbAARViwDi8bsQ4gPlmwAEVYsAAvG7EAED5ZsABFWLAELxuxBBA+WbICBCAREjmwAi+wDhCxDwGwCitYIdgb9FmwBBCxFwGwCitYIdgb9FmwABCxKwGwCitYIdgb9FmwAhCwL9CwIBCxNgGwCitYIdgb9FkwMQUmJwYnLgInJjc3EgA3BwYGAgYXFhYXMjcmEzc2EjYXFhYXFxYHBwIHFhcBFhYXNhM3NjU0JyYDBwYFStKkq6KQ6ZAQCQwaLgE44Bhvmj8JBgyZfzEypSUgGJLGdpG1EwQBByIx209p/gADRT6tLCIKf6s2JAkXB0FJBAJ/6pZXVqsBKwFSBdQCzv6IeDyOpwMI8AEW0aQBCH0DBNG1N0I92v7awg4CAaRamjmNAQDiUzLOBwj+xu89AAACAEv/6gSSBFIAJwAyAJCyGzM0ERI5sBsQsCnQALAARViwHS8bsR0cPlmwAEVYsAwvG7EMHD5ZsABFWLAALxuxABA+WbAARViwBC8bsQQQPlmyAgQdERI5sAIvsAwQsQ0BsAorWCHYG/RZsAQQsRQBsAorWCHYG/RZsAAQsScDsAorWCHYG/RZsAIQsCrQsB0QsTABsAorWCHYG/RZMDEFJicGJyYmAjc3NhI3BwYHBxUWFhczNyY3Nz4CFxYWFxYHBwYHFhcBBhc2PwI0JyYHBE2zh4mBjtBgEQca870WlyYOBWdbFxZfFhMSbZpae5IGAgURIZ45Yf5pEV9rFw8GS28dFAQ0OgICmgEImDvcAQsGyhP+eE1vhQMCqcaOesRcAwTBnjQvftWWCwIBjqdwZaSBV5kDBvYA////w/6aBUcFsAAmADwAAAAHAlEDtgAA////uf6aBBMEOgAmAFwAAAAHAlECwgAAAAEAnf6hBm4FsAAPAFEAsA0vsABFWLAILxuxCCA+WbAARViwAi8bsQIgPlmwAEVYsA4vG7EOED5ZsAIQsQABsAorWCHYG/RZsAXQsA4QsQYBsAorWCHYG/RZsArQMDEBITchByEDIRMzAzMDIxMhAfP+qiMDoyP+qrgCbdn22atz4z778ATsxMT73gTm+xz91QFfAAABAFb+vwTYBDoADwBNALANL7AARViwAy8bsQMcPlmwAEVYsA8vG7EPED5ZsAMQsQQBsAorWCHYG/RZsADQsA8QsQYBsAorWCHYG/RZsAMQsAjQsAYQsArQMDEBIzchByMDIRMzAzMDIxMhAUTuIgKwI9R4AZeb7ZqPbdg4/OoDd8PD/UsDePyI/f0BQQD//wDF/poFagWwACYA4QAAAAcCUQQ8AAD//wBw/poEOQQ7ACYA+QAAAAcCUQM7AAAAAQC5AAAFXAWwABgAULIFGRoREjkAsABFWLAALxuxACA+WbAARViwCy8bsQsgPlmwAEVYsA4vG7EOED5ZsgUOABESObAFL7AI0LAFELEUAbAKK1gh2Bv0WbAR0DAxAQMGFxYXEzMDNjcTMwMjEwYHByM3JiY3EwISSwcFDKk7nzhecHv3/fdrUX8uoC/Y0xdLBbD+NToujREBK/7bCxgCqPpQAj0WDOznDPbPAckAAQCFAAAENAQ7ABUAULIEFhcREjkAsABFWLAKLxuxChw+WbAARViwFC8bsRQcPlmwAEVYsAAvG7EAED5Zsg8UABESObAPL7EGAbAKK1gh2Bv0WbAD0LAPELAS0DAxISMTBwcjNyYmNxMzAwcGFxMzAzcTMwN57kV1HaAfnZsSNuw4BANZNaA1dGDtAWoTi40X26QBTP6yQGsiAQv+7hQCDQAAAQAXAAAEvQWwABEAR7IOEhMREjkAsABFWLABLxuxASA+WbAARViwAC8bsQAQPlmwAEVYsAkvG7EJED5ZsgUBABESObAFL7EOAbAKK1gh2Bv0WTAxMxMzAzYXFhYHAyMTNiYnJgcDF/33a5iv5vAZTPdMEF6DjcV8BbD9wSwEAvXa/jgByX+DAwMq/VkAAAIAYv/qBcEFyAAhACwAZ7IcLS4REjmwHBCwK9AAsABFWLAQLxuxECA+WbAARViwAC8bsQAQPlmyIwAQERI5sCMvsRYBsAorWCHYG/RZsAXQsCMQsAzQsAAQsR0BsAorWCHYG/RZsBAQsSkBsAorWCHYG/RZMDEFJiQCNzcmJjcXBxQXNhIkFxYSFxYHByUHBhcWFhcWNxcGASU3NicmJicmBgcDaLD+93QeDYOBCbACXiW8AQuf0OkFAQsW/LoMDwoOm4Cdwx10/fECWwcLAwV2aIfENxYBpAEhr0gc06UBRHQotAEhmQQE/urqUlGJAThTSnWIAwNIyFMDZQUhQkJwgQMFxs8AAAL/9P/qBIMEUwAcACYAlLINJygREjmwDRCwHtAAsABFWLAOLxuxDhw+WbAARViwAC8bsQAQPlmyIQ4AERI5sCEvtL8hzyECXbRfIW8hAnGyvyEBcbQfIS8hAnGyjyEBXbTvIf8hAnGxEgewCitYIdgb9FmwBNCwIRCwC9CwABCxFwGwCitYIdgb9FmyGQ4AERI5sA4QsR0BsAorWCHYG/RZMDEFLgI3JiY3FwcGFzYkFxYSBwchBhYWFxY3FwYGAyYGBwU3NicmJgJti9BhFGloB6QEA0JJARqzyskeD/1XBy1oSZqAeEPiD16NNQHBBQcFClgUA4jtiSC7lAE4Xy3T6QUF/tnqaFGBTQIFiX1hawOiA32QAhYuLEdSAAABACP+vQVbBbAAGQBgshgaGxESOQCwEC+wAEVYsAQvG7EEID5ZsABFWLAILxuxCCA+WbAARViwAi8bsQIQPlmyBwQCERI5sAcvsRgBsAorWCHYG/RZsgoHGBESObAQELERAbAKK1gh2Bv0WTAxASMDIxMzAzMBIQEWEgcCAAc3NjYSJyYmJycBlQhz9/33amQCDgE8/bfIyBgb/sf8E3GcSA0NhHL9AnL9jgWw/aQCXP2GH/7M4/73/soEwwSJAQF3bXkEAgABACH+5wSABDoAFgBgsgYXGBESOQCwBi+wAEVYsBEvG7ERHD5ZsABFWLAVLxuxFRw+WbAARViwDy8bsQ8QPlmyEw8RERI5sBMvsQ4BsAorWCHYG/RZsgAOExESObAGELEHB7AKK1gh2Bv0WTAxARYWBwYGByc2Njc2JicnAyMTMwMzASECt4+WDg/yskJ1hgwOcm62S+y87EtIAYMBNwJcKuado/cusiWRYm2HBgH+VAQ6/lABsAD////K/n4FfAWwACYA3QAAAAcAEARR/8b///+//n4EdwQ6ACYA8gAAAAcAEANj/8YAAQAu/kYFggWwABQAdrIKFRYREjkAsABFWLAALxuxACA+WbAARViwAy8bsQMgPlmwAEVYsBIvG7ESED5ZsABFWLAILxuxCBI+WbICABIREjl8sAIvGLRgAnACAl20MAJAAgJdsAgQsQ0BsAorWCHYG/RZsAIQsRABsAorWCHYG/RZMDEBAyETMwEGBiciJzcWMzI3EyEDIxMCIG4Cam/3/v4Y1qY3TiM2KYAhb/2Wa/b8BbD9gwJ9+he4yQITxw7EApH9lwWwAAABABH+RwQ/BDoAFABvsgsVFhESOQCwAEVYsAAvG7EAHD5ZsABFWLADLxuxAxw+WbAARViwEi8bsRIQPlmwAEVYsAgvG7EIEj5ZsgIDEhESOXywAi8YtEACUAICXbAIELENAbAKK1gh2Bv0WbACELEQAbAKK1gh2Bv0WTAxAQMhEzMDBgYjIic3FjMyNxMhAyMTAbtPAZdP7cMYzaM7SB49I4AhUv5pTO68BDr+PQHD+4e0xhLBEMIB6f5LBDr//wAn/n4FhwWwACYALAAAAAcAEARe/8b//wAZ/n4EdAQ6ACYA9AAAAAcAEANg/8b//wAn/n4GzgWwACYAMQAAAAcAEAWd/8b//wAi/n4FyAQ6ACYA8wAAAAcAEAS0/8YAAgBI/+gFNwXDABoAJABhshUlJhESObAVELAc0ACwAEVYsAAvG7EAID5ZsABFWLAKLxuxChA+WbIQAAoREjmwEC+wABCxFQGwCitYIdgb9FmwChCxGwGwCitYIdgb9FmwEBCxHgGwCitYIdgb9FkwMQEWBBcWBwcGAgQnJiYCNzcFNicmJicmByc2NhMWNjchBwYXFhYC770BDz0/GRAdyv7WrLPyZBoWA68PChKqi6TRHkDBDJHaQ/1FBw4KEJEFwwKumqDKbsb+vK8EBKoBMMWPAVtTh5cDA0nJKSv6/APL0SJOQ2x3AAEAL//mBJwFsAAbAG2yGRwdERI5ALAARViwAi8bsQIgPlmwAEVYsAwvG7EMED5ZsAIQsQABsAorWCHYG/RZsgQAAhESObIbDAIREjmwGy+xGQewCitYIdgb9FmyBRsZERI5shAMGRESObAMELETAbAKK1gh2Bv0WTAxASE3IQcBFhYHDgInJiY3MwYWFxY2NzYmJyc3A1P9riQDdx3+RaiwDguW+5PI6Aj0BG1ab60QEXSBlyAE5Myu/lUZ76+GyWsEBOy7ZHkCBH9vgYsEAbcAAAH/8P5yBFQEOgAbAGCyCxwdERI5ALAML7AARViwAi8bsQIcPlmxAAGwCitYIdgb9FmyBAACERI5shsMAhESObAbL7EZB7AKK1gh2Bv0WbIFGRsREjmyDwIMERI5sAwQsRMBsAorWCHYG/RZMDEBITchBwEWFgcOAicmJjcXBhYXFjY3NiYnJzcDCf22IwNyHP5FpbUPC5b4ksbnCOwEa19ysRARdoKaIAN2xKb+ShnrsIXIawME67oBZH4CBINwg4oEAbYA//8AM/5GBPwFsAAmALFQAAAmAiauKQAHAlQBLAAA//8ACf5EA9sEOgAmAOxSAAAnAib/if96AAcCVAEC//7////D/kYFRwWwACYAPAAAAAcCVAOmAAD///+5/kYEEwQ6ACYAXAAAAAcCVAKyAAAAAgAwAAAE+AWwAAwAFQBSsgwWFxESObAMELAP0ACwAEVYsAEvG7EBID5ZsABFWLADLxuxAxA+WbIAAQMREjmwAC+wAxCxDQGwCitYIdgb9FmwABCxDgGwCitYIdgb9FkwMQETMwMlLgI3PgIzExMlBgYHBhYXA6Rd9/39+YvSZwsLmf+ZsFr+7oCtDxFvaQObAhX6UAEEc8yEjNVz/S4CBgICj3dvjAQAAgBFAAAGgAWwABgAIQBcshkiIxESObAZELAJ0ACwAEVYsAovG7EKID5ZsABFWLAYLxuxGBA+WbIIChgREjmwCC+wGBCxDAGwCitYIdgb9FmyEgoYERI5sBnQsAgQsRoBsAorWCHYG/RZMDElLgI3PgIzBRMzAxc2NjU0JxcWFxIAIyUTJQYGBwYWFwIIi9JmCwua/ZkBLl322Tt/mhXmEgYQ/t/5/tda/ux9rhEPbmkBBHTLhIzWcgECFfsaAgLm311YAVlb/tb+m8oCBgICjXhwjAQAAAIAR//mBlEGGAAjADIAg7IGMzQREjmwBhCwJNAAsABFWLAHLxuxByI+WbAARViwGi8bsRoQPlmwAEVYsB8vG7EfED5ZsgQHHxESObAEL7IGBx8REjmwGhCxDgGwCitYIdgb9FmyEwcfERI5sh0HHxESObAEELEmAbAKK1gh2Bv0WbAfELEvAbAKK1gh2Bv0WTAxEzYSNhcWFxMzAwYXFhYXFhITNicXFhcWAgQnIiYnBicmJicmASYnJgYHBwYXFhYXFjc3TxWKy4GcWW3tzQMDAzcvjq8HAhLfDgQHi/71qXafHIa/mbIHAwLRN3d8nRUDBgECWlJ+ZQYCB7ABFYYDBHcCRPtOHh83QAMJASsBDWRkAWRj2/6ivQNaWbgEBNO4OwFuYwQCz7EUMzhmcwIEdUUAAQCq/+gFugWwACoAZrIVKywREjkAsABFWLANLxuxDSA+WbAARViwJy8bsScQPlmyBisNERI5sAYvsQMBsAorWCHYG/RZsA0QsQwBsAorWCHYG/RZshQDBhESObAnELEbAbAKK1gh2Bv0WbIgDScREjkwMQE2JicnNxcyNjc2JyU3BRYWBwYGBxYWBwcGFhcWEhM2JxcWFxYCBicmJjcCZAlVV+Ekj5WkDhnm/p0kAS/v9Q8IkZliXwkHBS0tgpoHAhHoDQQHif+nl54IAXtlewUCzQF4dL8JAc0BAdbAb6s+IqR+RjZIAgkBMAEBZGQBZGPd/qS9AgKwmwAAAQBh/+MEzQQ6ACkAY7IlKisREjkAsABFWLAfLxuxHxw+WbAARViwEC8bsRAQPlmxAwGwCitYIdgb9FmyCRAfERI5shgqHxESObAYL7EXAbAKK1gh2Bv0WbAfELEeAbAKK1gh2Bv0WbImFxgREjkwMSUVFhcWNjYnJicXFhcWAgYnJiY3NzYnJzcXNjc2JyU3FxYWBwYGBxYWBwKuAzdJcj0FBBTeEQkScOWVl5EFCQuD8B+lzhQVq/70HPa9zAgFY2tPRgbpITMDBWzVeU9OAU5Omv7WoAEDfHRMcQcCvQEGiYQKAcMBBaaPT3UvGnhSAAABAJL+uQPZBbAAJwBisiQoKRESOQCwGy+wAEVYsAovG7EKID5ZsABFWLAfLxuxHxA+WbIBKAoREjmwAS+xAAGwCitYIdgb9FmwChCxCQGwCitYIdgb9FmyEAABERI5sB8QsRgFsAorWCHYG/RZMDETNxcyNjc2JiclNxcWFgcGBRYWFxYPAjcHBgcnNjcHJicmNzc2JieSIrWOpw4Obmv+2h/45fIPEf76R1QIBAcWA88aKMeDZCyVJQQDChIOXV4CXMMBeXNtcQQBwwEB3sDedR54VDM1dwwEoPecUYdvAS5HLEx9bYAEAAABAIz+qAO5BDoAIwBish8kJRESOQCwGS+wAEVYsAkvG7EJHD5ZsABFWLAdLxuxHRA+WbIBJAkREjmwAS+xAAGwCitYIdgb9FmwCRCxCAGwCitYIdgb9FmyEAABERI5sB0QsRUBsAorWCHYG/RZMDETNxc2NzYmJyU3BRYWBwYGBxYXFgcHNwcGByc2NwcmNzc2JieMH9LWFwpUVP7aHgENvdUKBWVnbg0EBga+GSbIg2somSMGDwlNTAGbswEGkENQAgHBAQWwkFB7MTR7JighAaHxoVGWcQEtToBOTgMAAAH/3v/lB0oFsAAjAGWyIyQlERI5ALAARViwDS8bsQ0gPlmwAEVYsCAvG7EgED5ZsABFWLAFLxuxBRA+WbANELEAAbAKK1gh2Bv0WbAFELEIAbAKK1gh2Bv0WbAgELEUAbAKK1gh2Bv0WbIZDQUREjkwMQEhAwICByM3NzY2NzcTIQMGFxYWFxYSEzYnFxYXFgIEJyYmNwRZ/m+QQ/nAXhczdJspFIsDdboDAwM1LomqBQIS6Q4EB47++KetrxIE4/1b/tT+8wXKAwzW6XICpvu5HR80QAMJASUBDGRkAWRj3/6jvQQEz64AAAH/3v/nBiYEOgAiAGWyACMkERI5ALAARViwDS8bsQ0cPlmwAEVYsAUvG7EFED5ZsABFWLAfLxuxHxA+WbANELEAAbAKK1gh2Bv0WbAFELEHAbAKK1gh2Bv0WbAfELESAbAKK1gh2Bv0WbIYDQUREjkwMQEjAwYGJyM3NzY2NzcTIQMGFhcWNjc3NicXFhcWAgYnJiY3AzD+YjfOoE0VJVtzHw5gAsx5CDw+boYNAgES3w4FCnntmayzEgN0/j/qzQTJAwiasE4Bzv0sUWUCBOncPF5eAV5ew/62tgMCzK8AAQAn/+YHUAWwAB4Ac7IWHyAREjkAsABFWLAALxuxACA+WbAARViwGi8bsRogPlmwAEVYsBIvG7ESED5ZsABFWLAYLxuxGBA+WbASELEGAbAKK1gh2Bv0WbILABgREjmyHQAYERI5fLAdLxi0MB1AHQJdsRYBsAorWCHYG/RZMDEBAwYXFhYXFhITNicXFhcWAgQnJiY3NyEDIxMzAyETBXi3AwMEMy2JqwUCEukOBAeO/vmpp68OJ/2Xa/b99m8CaW8FsPu3HR42PwEIASIBDmRkAWRj4P6juwMCzrH//ZcFsP2DAn0AAAEAC//mBikEOgAeAHayCB8gERI5ALAARViwBC8bsQQcPlmwAEVYsAgvG7EIHD5ZsABFWLAbLxuxGxA+WbAARViwAi8bsQIQPlmyBwgCERI5fLAHLxiyUwcBXbJABwFdsQABsAorWCHYG/RZsBsQsQ8BsAorWCHYG/RZshQIAhESOTAxASEDIxMzAyETMwMGFxYWFxYSNzQnFxYXFgIGJyYmNwLn/l5N7bztTgGiTe15AwMFOzB3jQIR3g4FCnjumamxDAG6/kYEOv5DAb39LB8gNkEBBgET715eAV5evv6yuAMCyrIAAQBM/+gElAXHACEASbIXIiMREjkAsABFWLAJLxuxCSA+WbAARViwAC8bsQAQPlmwCRCxDgGwCitYIdgb9FmwABCxFwGwCitYIdgb9FmyHAkAERI5MDEFJiYCNxM2EiQXFhcHJicmBgcHBhcWFhcWNicmJxcXFgIEAlKj8nEWKRy/ASKszI9Qepui6h4oCgkNjW+TrgEBDesNCov+8hUEpAEcpgEGswEemwEEWLZFAgLuvv1GSnmTAwLQ4lhXAa7W/u+WAAEAPf/nA6oEUQAfAEWyACAhERI5ALAARViwEy8bsRMcPlmwAEVYsAovG7EKED5ZsQABsAorWCHYG/RZsAoQsATQsBMQsRgBsAorWCHYG/RZMDElFjY3JzMXFgYGJy4CNzc+AhcWFwcmIyIGBwYXFhYCBVliAgXfCAZszH6Ny18OBRKZ8pGobUFdgXiqFwsGCWyvAmmWbm2ew2UDBI71lCqZ/YwBAkS7Pb+dXz9oegAAAQCQ/+YFNAWwABoAT7IJGxwREjkAsABFWLACLxuxAiA+WbAARViwFy8bsRcQPlmwAhCxAAGwCitYIdgb9FmwBNCwBdCwFxCxCgGwCitYIdgb9FmyEAIXERI5MDEBITchByEDBxYWFxYSNzc2JxcWFxYCBCcmJjcCRf5LJARfJP5MlgEDNS6HpwsBAhLoDgMHif74q6ivDgTjzc38hTs0QAMGARH/HmRkAWRj2f6hwAMCzrEAAQBz/+gElwQ6ABkAT7IKGhsREjkAsABFWLACLxuxAhw+WbAARViwFi8bsRYQPlmwAhCxAAGwCitYIdgb9FmwBNCwBdCwFhCxCwGwCitYIdgb9FmyEAIWERI5MDEBITchByEDBhcWFhcWNicmJxcWBwYEJyYmNwGv/sQiA3Ij/rhYAwMFOzF3iAoFFN0pDhn+98Kpsg4Dd8PD/e8fIDdAAQTssEtKAbR3zfsCAsyvAAABAFb/6AUiBcgAKwB3shssLRESOQCwAEVYsBwvG7EcID5ZsABFWLAOLxuxDhA+WbIpHA4REjmwKS+yHykBcbJKKQFdsQABsAorWCHYG/RZsA4QsQYBsAorWCHYG/RZsgocDhESObIUACkREjmyHxwOERI5sBwQsSMBsAorWCHYG/RZMDEBIgYHBhYXFjY3NwYGBCcuAjc2JSYnJjc2NiQXFgQHJzYmJyYGBwYWFxcHAsOguw8Nm4eCvxH1C6H+9Zuc+ncKEQEwUDE+BgifARCm1QEIBPQEhm6NwQ8Og4S9JAKDfHdjdwMCfmUBhcJmAwJuu3r7ZyxDVWaIwGQDBeG1AV1vAgN5Z2VrAQHIAP///8r+RgWLBbAAJgDdAAAABwJUBEwAAP///7/+RgScBDoAJgDyAAAABwJUA10AAAACAOwEcQNgBdgABQAOACMAsAwvsATQsAQvsADQGbAALxiwDBCwBtCwBi+wAtCwAi8wMQETNwcBBwMzBwYWFwcmNwH1nc4B/vFd660PCQ4mTZgQBJkBPgEY/sMBAVVTPGQwQ12xAP//ADYCCQJYAs0ABgARAAD//wA2AgkCWALNAAYAEQAA//8AnAJtBKQDMQBGAa/hAEzNQAD//wCDAm0F4wMxAEYBr4kAZmZAAAAC/1L+YAMXAAAAAwAHADoAsABFWLAGLxuxBhA+WbAC0LACL7YAAhACIAIDXbEBA7AKK1gh2Bv0WbAGELEFA7AKK1gh2Bv0WTAxASE3ITchNyECzvyEHAN8EfyEHAN8/mCdZ5wAAQCuBCACIgYaAAcAHbIHCAkREjkAsABFWLAALxuxACI+WbAE0LAELzAxARcGBwcjNzYBq3drHB3QFCYGGk+Nf5985wABAIoEAAH+BgAABwAdsgIICRESOQCwAEVYsAQvG7EEIj5ZsADQsAAvMDEBJzY3NzMHBgEBd2ocHtAWJQQAT4uBpYjiAAH/pP7WARUAygAHABmyBwgJERI5ALAIL7EEDbAKK1gh2Bv0WTAxEyc2NzczBwYadmYbHNQTI/7WUImBmnvgAAABAM0EAQHSBgAACgATsggLDBESOQCwAC+wBtCwBi8wMQEHBhcWFwcmJjc3AcAZDAoJJHtFRQwWBgCRTkhJRklHyGKO//8AtwQgA3EGGgAmAYQJAAAHAYQBTwAA//8AlwQAA08GAAAmAYUNAAAHAYUBUQAAAAL/of7CAlsA/wAIABEAIrINEhMREjmwDRCwBdAAsBIvsQQNsAorWCHYG/RZsA3QMDETJzY3NzMHBgYXJzY3NzMHBgYbem8aINQdEnvfenQZINUeEn7+wlCglLm2cc9HUKORubd0yQAAAQBpAAAESwWwAAsATACwAEVYsAgvG7EIID5ZsABFWLAGLxuxBhw+WbAARViwCi8bsQocPlmwAEVYsAIvG7ECED5ZsAoQsQABsAorWCHYG/RZsATQsAXQMDEBIQMjEyE3IRMzAyEEK/6Uiu6L/pcgAWc77jsBbQNy/I4DcsgBdv6KAAH/+/5gBGUFsAATAH4AsABFWLAMLxuxDCA+WbAARViwCi8bsQocPlmwAEVYsA4vG7EOHD5ZsABFWLACLxuxAhI+WbAARViwAC8bsQAQPlmwAEVYsAQvG7EEED5ZsQYBsAorWCHYG/RZsA4QsQgBsAorWCHYG/RZsAnQsBDQsBHQsAYQsBLQsBPQMDEhIQMjEyE3IRMhNyETMwMhByEDIQO8/pNB7UH+mR8BZmz+mR8BZzruOwFtH/6UbQFu/mABoMICtMQBdv6KxP1MAAEAngIEAk0D3AANABeyAw4PERI5ALADL7AKsAorWNgb3FkwMRM2NjMWFhUHBgYjIiY1nwZ9YFtwAgd9X1pwAvxkfAJ2Xitkc3RbAP//ADP/8gMCAQMAJgASAwAABwASAb8AAP//ADP/8gSuAQMAJgASAwAAJwASAb8AAAAHABIDawAAAAEAWwHpAWYC7wATABmyDxQVERI5ALAEL7EODbAKK1gh2Bv0WTAxEzQ+Ajc2HgIVFA4CBwYuAlsTIzEdHDIkFRQkMRwcMSQVAmcdMSQUAQESIi8dHTAjFAEBEiEuAAAGAJ3/6AcGBccAFgAkACgANgBEAFIAvLICU1QREjmwAhCwGdCwAhCwJ9CwAhCwK9CwAhCwONCwAhCwTdAAsCUvsCcvsABFWLAXLxuxFyA+WbAARViwEy8bsRMQPlmwA9CwAy+yBQMTERI5sAfQsAcvsBMQsA7QsA4vshETAxESObAXELAe0LAeL7ATELEsArAKK1gh2Bv0WbADELEzArAKK1gh2Bv0WbAsELA60LAzELBB0LAeELFIArAKK1gh2Bv0WbAXELFPArAKK1gh2Bv0WTAxATY2FxYXNhcWFgcHBgYnJiYnBicmJjcDFhYHBwYGJyYmNzc2NhMnARcBBhYXFjY3NzYmJyYGBwUGFhcWNjc3NiYnJgYHAQYWFxY2Nzc2JicmBgcC6w6+hJQ8Z4J9lQgGDbqHQHEgZoJ9lQb2gJYIBw28gXqVCAULtQJ4A295/q8FOjdBVAsJBzo5PlcLAbAFOjg/VQsKBzo5PloJ+/cFOjc9VgwKBTg6PVYMAWSGqwIFa3ACAqqARIytAgE2OG8CAqp/BK4EqoBKiKoEAqt/QIyw+qhPBGdP/D9FUwICWEZPQlYCAlhFUEVTAgJXR09CVgICWkoC60hQAgJWSE1FVQICVkkAAQBdAIoCZQOpAAYAEACwBS+yAgcFERI5sAIvMDEBEyMDNwEzASamlNsBAVSzAgz+fgGFFAGGAAAB//kAigICA6kABgAQALAAL7IDBwAREjmwAy8wMQETBwEjAQMBJtwC/q20AT+lA6n+fBX+egGbAYQAAf/hAG8DyQUlAAMACQCwAC+wAi8wMTcnARdaeQNweG9PBGdPAP//AGQCmwLtBbEDBwIgAHMCmwATALAARViwCS8bsQkgPlmwDdAwMQAAAQBuAowDUwW6ABIATbIPExQREjkAsABFWLAELxuxBCA+WbAARViwAC8bsQAgPlmwAEVYsBAvG7EQFD5ZsABFWLAILxuxCBQ+WbAEELENA7AKK1gh2Bv0WTAxARc2NjMyFgcDIxM3NicmBwMjEwGKAjRsQXJ0D1LBSwQEX1Y/YcGLBa16SD+njP4FAco9fwICW/3RAyAAAf/2AAAEpQXJACYAnrIWJygREjkAsABFWLAXLxuxFyA+WbAARViwBi8bsQYQPlmyJRcGERI5sCUvsQACsAorWCHYG/RZsAYQsQkBsAorWCHYG/RZsATQsAQvsAAQsA3QsCUQsA/QsA8vsCUQsBPQsBMvtg8THxMvEwNdsRACsAorWCHYG/RZsBcQsR0BsAorWCHYG/RZshsTHRESObATELAh0LAQELAj0DAxASEHBgclByE3FzY3Nwc3MzcjNzM3NiQXFhYHJzYnJgYHByEHIQchAwP+4QcUWwKoJPwEJEVkHwqoGpsSmBmTExgBFce0ywjvCapafg4SATYa/tARAS0B1C2BXwPKyQEksTgBkXmQoMb1BgTZtgHFBAKFaaCQeQAFAA0AAAZfBbAAGwAfACMAJgApAL+yCiorERI5sAoQsB/QsAoQsCHQsAoQsCbQsAoQsCjQALAARViwGi8bsRogPlmwAEVYsBcvG7EXID5ZsABFWLAMLxuxDBA+WbAARViwCS8bsQkQPlmyBQkaERI5sAUvsAHQsAEvsg8BAV2xAwOwCitYIdgb9FmwBRCxBwOwCitYIdgb9FmwJdCwCtCwDtCwBRCwHdCwIdCwEdCwAxCwHtCwItCwEtCwARCwGdCwJ9CwFdCwCRCwJNCwFxCwKdAwMQEzByMHMwcjAyMDIQMjEyM3MzcjNzMTMxMhEzMBMzcjBTMnIwE3BwE3JwWN0hzRG9Ic0Vbv2P6xVvZWzRzMG80czFbu1gFTVvX96pUb8v5g7kKRAjATL/4HKhsDxaCXoP4SAe7+EgHuoJegAev+FQHr/N6Xl5f+fU4DAdUDRgAAAgAr/+0GWAWwACAAKQCmsiYqKxESObAmELAY0ACwAEVYsBcvG7EXID5ZsABFWLAcLxuxHBw+WbAARViwHy8bsR8cPlmwAEVYsBQvG7EUED5ZsABFWLALLxuxCxA+WbAfELEAAbAKK1gh2Bv0WbALELEGAbAKK1gh2Bv0WbAAELAP0LAQ0LIiFBcREjmwIi+xEgGwCitYIdgb9FmwHxCwHtCwHi+wFxCxKAGwCitYIdgb9FkwMQEjAwYXFhcyNwcGJyYmNxMjAiEnAyMTBR4CBzcTMwMzARc2NzYnJicnBjm5ZwMCBkomLxFLSnt7DWVpgv5wm170/AFzfL9oBHku7S65+0iCykIjCxOgmwOG/aIZFEEDCb4VAQKjiQJq/pQB/eUFsAEDXKhvAQEH/vn+rQIDrFxdjggBAP//ACf/6QgUBbAAJgA2AAAABwBXBFAAAAAGABwAAAY7BbAAHwAjACcAKwAuADEBA7IoMjMREjmwKBCwHtCwKBCwItCwKBCwJNCwKBCwLdCwKBCwL9AAsABFWLACLxuxAiA+WbAARViwDC8bsQwQPlmwAEVYsBAvG7EQED5ZsggCDBESObAIL7AE0LAEL7AA0LAEELEGA7AKK1gh2Bv0WbAIELEKA7AKK1gh2Bv0WbIoAgwREjmwKC+xDgOwCitYIdgb9FmwChCwEtCwCBCwFNCwBhCwFtCwBBCwGNCwAhCwGtCwBBCwHNCwAhCwHtCyIAIMERI5sCAvsAYQsCLQsCAQsCTQsAYQsCbQsioCDBESObIsAgwREjmwIBCxLQOwCitYIdgb9FmyLwIMERI5sDDQMDEBMxMzAzMHIwczByMDIxMjAyMDIzczJyM3MwMzAzMTMwEzNyMFMzcjBTc3IwE3IwU3IwP0zID7kYEcoD3BHODY9At3w/IO4B2/A6EcgAnwB9F/3P3PbTafAh9oNpr+/UQLEf6YRTwCGUI6BCoBhv56oKKg/bgCSP24AkigoqABhv56AYb9OKKioqUBpv38wri4AAIAEP/8BjYEOgAOABsAarIAHB0REjmwEdAAsABFWLAOLxuxDhw+WbAARViwFi8bsRYcPlmwAEVYsAwvG7EMED5ZsABFWLAPLxuxDxA+WbESAbAKK1gh2Bv0WbAOELELAbAKK1gh2Bv0WbIFEgsREjmyEAsSERI5MDEBFhYHAyMTNicmJyUDIxsCMwMFFjcTMwMGBCcDOaSXFTPuNQUCCoP+rprtu9F/7V0BOcgnde5xG/71zgQ5BczE/sABQiwleAUC/IoEOvvGAtb97QICxAK3/VvE1QQAAAEATv/tBJ4FxgAmAI6yDCcoERI5ALAARViwGS8bsRkgPlmwAEVYsAsvG7ELED5ZsiYZCxESObAmL7EAArAKK1gh2Bv0WbALELEGAbAKK1gh2Bv0WbAAELAQ0LAmELAR0LAmELAW0LAWL7YPFh8WLxYDXbETArAKK1gh2Bv0WbAZELEeAbAKK1gh2Bv0WbAWELAh0LATELAj0DAxASEGFxYWFxY3FwYnLgI3BzczNyM3MxIAFxYXByYnJgYHIQchByEDRP6rCQgLemlbcwd6c5ndZRSvGaYXqBmgQgFI8GOMMV9flMIuAWEZ/qcXAVoCD0Q9Y3EDAiLPGwIDivmbAY2AjQEHARYCAh7NIwICrqeNgAAEAEIAAAYPBbAAGgAfACQAKQDfshoqKxESObAaELAd0LAaELAj0LAaELAo0ACwAEVYsAsvG7ELID5ZsABFWLABLxuxARA+WbALELEkAbAKK1gh2Bv0WbAK0LAKL0ARAAoQCiAKMApAClAKYApwCghdsQcDsAorWCHYG/RZsAbQsAYvQAsABhAGIAYwBkAGBV2xAwOwCitYIdgb9FmwJ9CwJy9ADzAnQCdQJ2AncCeAJ5AnB12xAAGwCitYIdgb9FmwChCwINCwIC+wD9CwDy+wBxCwHdCwEtCwBhCwHtCwHi+wFNCwFC+wAxCwJtCwF9AwMQEDIxMjNxc3BzczEwUyFhczBycGBzcHBwYEIwE3IQchJSUmJyUBBQclNgG/XveLsx2tFbgdsi8B/LTqJekdsQgPvh7OUf7+tgFNCf3OFAIw/fgB4y92/tUBlP4dEQEbdwId/eMDH6ACTAKgAQkBjHygAikkA6ABg38BxClM6AQ5AQP+PAE7AgEAAAEAOwAABIcFsAAZAGmyEBobERI5ALAARViwGC8bsRggPlmwAEVYsAwvG7EMED5ZsBgQsRcBsAorWCHYG/RZsADQsBcQsBPQsBMvsAPQsBMQsRIHsAorWCHYG/RZsAbQsBIQsA7QsA4vsQkHsAorWCHYG/RZMDEBIxYHNwcjBgYHARUhATcXMjcFNyEmJyU3IQQ01RsE0VCNN+3QAWb+7v5xGOnLZf3tUQHUDsL+5VkDmwT5VlsBtqirFP3jDwJcjgKtAraVBQHMAAABABD/5wRHBbAAHgCUshsfIBESOQCwAEVYsBEvG7ERID5ZsABFWLAFLxuxBRA+WbITEQUREjmwEy+wF9CwFy+yABcBXbEYAbAKK1gh2Bv0WbAZ0LAI0LAJ0LAXELAW0LAL0LAK0LATELEUAbAKK1gh2Bv0WbAV0LAM0LAN0LATELAS0LAP0LAO0LAFELEaAbAKK1gh2Bv0WbIeBREREjkwMQEHBgIEJyYnEwc/Agc3NxMzBzcPAjcHBwM2Ejc3BEcIG8X+27B0g2LlJeQW5SXkNvcl6iXpF+sl6l2u3h8IAv9M0/61rgICFQJXVtFXflbSVwE20VnSWn5Z0ln9/gUBB+xNAAH/5AAABKwEOgAaAF2yDRscERI5ALAARViwGS8bsRkcPlmwAEVYsAYvG7EGED5ZsABFWLANLxuxDRA+WbAARViwEi8bsRIQPlmyAA0ZERI5sAAvsQwBsAorWCHYG/RZsA/QsAAQsBjQMDEBFhYXFgcHIzc3NiYnAyMTBgMHIzcSAD8CMwM6nbsRCQ4d7SEIBU1Tee56+EYm7SM0ASzaDCvtA2go+rxvbK/OaYG3KP1pAphh/qbdywEZAVopAtEAAAL/5gAABWAFsAAWAB8Ae7IYICEREjmwGBCwDdAAsABFWLAMLxuxDCA+WbAARViwAi8bsQIQPlmyBgIMERI5sAYvsQUBsAorWCHYG/RZsAHQsAYQsArQsAovsg8KAV2xCQGwCitYIdgb9FmwFNCwBhCwFdCwChCwF9CwDBCxHwGwCitYIdgb9FkwMSUhByM3IzczNyM3MxMFMgQHBgQjJQchAQUyNjc2JiclAtv+ySn2KMckxhPHI8d8AffmAQEREv7G9f7LEwE5/v0BF4WwEQ5za/7L5+fny2vLAsgB+MrZ+AFrATYCh39uhQQBAAAEAML/5wU+BckAHAAqADgAPACYsgE9PhESObABELAo0LABELAs0LABELA50ACwOS+wOy+wAEVYsAovG7EKID5ZsABFWLAkLxuxJBA+WbAKELAD0LADL7IOAwoREjmwChCxEQKwCitYIdgb9FmwAxCxGQKwCitYIdgb9FmyHAMKERI5sCQQsB3QsB0vsCQQsS4CsAorWCHYG/RZsB0QsTUCsAorWCHYG/RZMDEBBgYnJiY3NzY2FxYWFSc2JiMiBgcHFRYWFzI2NwEWFgcHBgYnJiY3NzY2AwYWFxY2Nzc2JicmBgcFJwEXAuwKoXt3jQgGDax/eYylAjIyN0wKCQItJzBDDgHifpcIBg23h36ZCAULuiQFPDY+VAwKBTo3P1gJ/ep5A296BCV4kAICq39Eja0CBJRzAThAWEVOMi44ATw3/mwCqIFEjK4EAqqAQo2s/oNHUgICVUpPSFACAltJ708EZ04AAgAx/+oD4gX/ABoAJABdshYlJhESObAWELAb0ACwDi+wAEVYsAAvG7EAED5ZsggADhESObAIL7EHB7AKK1gh2Bv0WbAV0LAAELEaAbAKK1gh2Bv0WbAIELAb0LAOELEhAbAKK1gh2Bv0WTAxBS4CNzcGBzc2NxM2NhcWFgcHBgAHBwYVFBcDNj8CNCcmBwcCZoO7UBYES3YUW2ZUGsuVgI4LBBT++sUPCJ1rxx0FAjZTGgcWB3PKfxARBbwCFQHfyN4FBLmMLLf+sGZOMy6YCwI/tNMlJVUFBZksAAQAIwAAB+kFxQADABEAHwApAKSyICorERI5sCAQsAHQsCAQsBDQsCAQsBPQALAARViwJS8bsSUgPlmwAEVYsCgvG7EoID5ZsABFWLAHLxuxByA+WbAARViwIC8bsSAQPlmwAEVYsCMvG7EjED5ZsAcQsA7QsA4vsAPQsAMvsQACsAorWCHYG/RZsA4QsRUCsAorWCHYG/RZsAcQsRwCsAorWCHYG/RZsiIlIBESObInJSAREjkwMQEhNyEBNjYXFhYHBwYGJyYmNxcGFhcWNjc3NiYnJgYHASMBAyMTMwETMwc9/a8bAlD95BHTl46lCwcQ1JWQpAqsCEVHTWoPCghESFBpDv4Q//7Ntu79/gE1t+wBnJUCLp/HBATDmkqoxQQExJcCYGkCA21jVV9rAgJxXvugBBT77AWw++kEFwAAAgDtA5MEywWwAAwAFABuALAARViwBi8bsQYgPlmwAEVYsAkvG7EJID5ZsABFWLATLxuxEyA+WbIBFQYREjmwAS+yAAkBERI5sgMBBhESObAE0LIIAQkREjmwARCwC9CwBhCwDbAKK1jYG9xZsAEQsA/QsA0QsBHQsBLQMDEBAwcDAyMTMxMTMwMjASMDIxMjNyEEPq48PENuX4I5w4debf5vhk1zTYkRAYIE9v6fAgF+/oMCHP6GAXr95AG9/kUBu18AAAIAff/pBHcEUgAWAB0AZbIUHh8REjmwFBCwGNAAsABFWLAKLxuxChw+WbAARViwAi8bsQIQPlmyGgoCERI5sBovsQ8MsAorWCHYG/RZsAIQsRMMsAorWCHYG/RZshYKAhESObAKELEXDLAKK1gh2Bv0WTAxJQYnJiYCNzYSJBceAgcHIQMWFxY2NwMmBwMhEyYDrLLChM9oDg6xAQOJgsBfCgX9Ezxdj1O6dcqKmjQCCjVcXHMEApcBAoyRARSZBASO+JEx/rZnBAM3RAMrA3z+6gEga///ALb/8gWJBZkAJwHGAEkChgAnAZQA8wAAAQcCJAMJAAAAEACwAEVYsAUvG7EFID5ZMDH//wCC//IGIQW4ACcCHwCOApQAJwGUAZsAAAEHAiQDoQAAABAAsABFWLANLxuxDSA+WTAx//8AiP/yBhYFqAAnAiEAfgKTACcBlAGAAAABBwIkA5YAAAAQALAARViwAS8bsQEgPlkwMf//ALX/8gXWBaMAJwIjAJICjgAnAZQBKgAAAQcCJANWAAAAEACwAEVYsAUvG7EFID5ZMDEAAgBF/+cESAX1AB0ALQBXsgguLxESObAIELAe0ACwDS+wAEVYsBUvG7EVED5ZsgANFRESObAAL7ANELEHAbAKK1gh2Bv0WbAAELEeAbAKK1gh2Bv0WbAVELEnAbAKK1gh2Bv0WTAxARYXNicmJicmBgcnNhcWEhMVAgIEJy4CNzc+AhcmBgcHBhcWFhcWNjc3JiYCZKRrAwIKhG5Fg0IMkaLQ3QYNnv75qYrDWxACEZHimXamFQMGBAVhV3qlIA0OdAQFBHsqMJWyBAMgFblDAQT+1/7qRv7X/nfSBAKK8ZMWkep9xgOolBU2OWRzAwXOzlVOWwAAAQAf/xsFVQWwAAcAKACwBC+wAEVYsAYvG7EGID5ZsAQQsAHQsAYQsQIBsAorWCHYG/RZMDEFIxMhAyMBIQRN7un9rentAQcEL+UF1PosBpUAAAH/p/7zBPoFsAAMADcAsAMvsABFWLAILxuxCCA+WbADELECAbAKK1gh2Bv0WbAF0LAIELEKAbAKK1gh2Bv0WbAH0DAxAQEhByE3AQE3IQchAQNz/ZQDIiL7oBwCuf49GQQoIv0EAZkCRf1xw6ICyALGjcP9dQABAJwCbQP4AzEAAwASALACL7EBAbAKK1gh2Bv0WTAxASE3IQPV/McjAzkCbcQAAQA0AAAFCQWwAAgAPbIACQoREjkAsAcvsABFWLABLxuxASA+WbAARViwAy8bsQMQPlmyAAEDERI5sAcQsQYBsAorWCHYG/RZMDEBATMBIwMjNyEB9wI13f0pwG7QIwFZAS0Eg/pQAkHFAAMASf/oB64EUgAeAC8AQQBksgZCQxESObAGELAp0LAGELA70ACwAEVYsAovG7EKED5ZsATQsAoQsBPQsBMvsBnQsgcZChESObIWGQoREjmwExCxPwGwCitYIdgb9FmwJNCwChCxNQGwCitYIdgb9FmwLNAwMQEGAgYnJiYnBgYnLgI3NzYSNhcWFhc2NhcWFhcWByc2JycmJyYGBwcGFhYXFjY3BQYXFhYXFjY2Nzc2JicmJyYGB58Sn/SPiNUuevCFhMRgDwISn/OOi9YtePGHicksJg3pBgQFIp513SoHBkZ6RXyyF/qLBgUHZlhLl38bBgQmJVFqe7ACGJv+/JEEBLKVtJsDBI79lBeXAQWRBASykrKZAwKeiHaCATU9Jb4FAtaGJEulaAIFyqMQNjxpfAMCXq5YJDd4M2wEBcsAAf8X/kUDIgYZABYAP7IBFxgREjkAsABFWLAOLxuxDiI+WbAARViwAy8bsQMSPlmxCAGwCitYIdgb9FmwDhCxEwGwCitYIdgb9FkwMQUGBiciJzcWMxY3EzY2FxYXByYjIgYHAR8VyqM5TSM5FY8bvhXXqjVnKTApUGUNT6+9BBW8DwSwBOuxxgIBFrgNYFMAAgAwAP4ENQP5ABIAJQB8sg4mJxESObAOELAg0ACwAi+wBtCwBi+wAhCwCNCwBhCxCwGwCitYIdgb9FmwAhCxEAGwCitYIdgb9FmwCxCwEtCwAhCwFdCwFS+wGdCwGS+wFRCwG9CwGRCxHgGwCitYIdgb9FmwFRCxIwGwCitYIdgb9FmwHhCwJdAwMRM2MzIWFjMyNwcGJyIuAiMGBwc2MzIWFjMyNwcGJyIuAiMGB45tjV3ZTS17ghZtfDxka2Y/hogzbYld20wteocYa4AxVqZVLoeDA5BpeRd92WsCKT0qAnzKaXkXfdlrAhxcGAJ8AAABAGIAggQUBMEAEwA5ALATL7EAAbAKK1gh2Bv0WbAE0LATELAH0LATELAP0LAPL7EQAbAKK1gh2Bv0WbAI0LAPELAL0DAxASEHJzcjNyE3ITchNxcHMwchByEDp/36o2pypCMBEaH+dCQB+KtqebEj/uGgAZkBZOJFncnfyutFpsrfAP///9UAFAPbBHAAZwAgABgAi0AAOZoABwGv/zn9p///ABcAEwPzBHMAZwAiABoAmEAAOZoABwGv/3v9pgACADoAAAPiBbAABQAJADiyBgoLERI5sAYQsATQALAARViwAC8bsQAgPlmwAEVYsAMvG7EDED5ZsgYAAxESObIIAAMREjkwMQEzEwEjAwEBEwECJb/+/hbA/gIq/sCUAT8FsP0a/TYC5AHH/h/+NwHjAP//AGkAqAIOBQoAJwASADkAtgAHABIAywQHAAIAZgJ/AoIEOQADAAcAM7IACAkREjmwBdAAsABFWLACLxuxAhw+WbAARViwBi8bsQYcPlmwAhCwANCwAC+wBNAwMQEjEzMTIxMzAQCaTZrnmk6aAn8Buv5GAboAAf/P/2cBFgEGAAcAFQCwBC+2MARABFAEA12wANCwAC8wMRcnNjc3MwcGSntfFQ/EDSSZT4V4U1bF//8AXwAABZEGGgAmAEoAAAAHAEoCMwAAAAIASwAABEwGGgAVABkAhbIHGhsREjmwBxCwF9AAsABFWLAILxuxCCI+WbAARViwAy8bsQMcPlmwAEVYsBIvG7ESHD5ZsABFWLAYLxuxGBw+WbAARViwAC8bsQAQPlmwAEVYsBYvG7EWED5ZsAMQsQEBsAorWCHYG/RZsAgQsQ4BsAorWCHYG/RZsAEQsBPQsBTQMDEzEyM3Fzc2NhcWFhcHJiMmBwc3ByMDISMTM0+coCCYDiP8w06VSjl+cNQoDdcgzp0CVe687QOGtAFRvtIEASYXyDMCykIBtPx6BDoAAQBfAAAEpAYZABgAb7ISGRoREjkAsABFWLATLxuxEyI+WbAARViwBi8bsQYcPlmwAEVYsA4vG7EOHD5ZsABFWLAKLxuxChA+WbAARViwFy8bsRcQPlmwExCxAgGwCitYIdgb9FmwBhCxCAGwCitYIdgb9FmwDNCwDdAwMQEmByIGBwczByMDIxMjPwI2NhcWFxcDIwOfbTVdeA8O1yDVne2doSCfDhrvu21t2v/sBUIQAV9eWrT8egOGtAFltsMCAhAg+hsAAgBfAAAGtQYaACcAKwDBshMsLRESObATELAp0ACwAEVYsBYvG7EWIj5ZsABFWLADLxuxAxw+WbAARViwES8bsREcPlmwAEVYsCAvG7EgHD5ZsABFWLAqLxuxKhw+WbAARViwCC8bsQgiPlmwAEVYsAAvG7EAED5ZsABFWLAjLxuxIxA+WbAARViwKC8bsSgQPlmwAxCxAQGwCitYIdgb9FmwCBCxDQGwCitYIdgb9FmwFhCxHAGwCitYIdgb9FmwARCwIdCwItCwJdCwJtAwMTMTIzczNzY2FxYXByYjJgYHBwU3NjYXFhYXByYnJgcHNwcjAyMTIQMhIxMzY52hIKANGd+uPFAaLC1VbA8PAWARJvjATpZKOnp00ygN1yDOne2c/pmdBKntvO0DhrRgt8kCAhK+CgFeU2YBYbbJAgImF8gxAgLKQgG0/HoDhvx6BDoAAAEAXwAABvkGGwAqAK6yEyssERI5ALAARViwCC8bsQgiPlmwAEVYsBYvG7EWIj5ZsABFWLADLxuxAxw+WbAARViwES8bsREcPlmwAEVYsCIvG7EiHD5ZsABFWLAALxuxABA+WbAARViwGi8bsRoQPlmwAEVYsCYvG7EmED5ZsAMQsQEBsAorWCHYG/RZsAgQsQ0BsAorWCHYG/RZsBYQsR4BsAorWCHYG/RZsAEQsCTQsCXQsCjQsCnQMDEzEyM3Mzc2NhcWFwcmIyYGBwclNzY2FxYXFwEjEyYjIgYHBzMHIwMjEyEDY52hIKANGeKtMlgaNyFVbA8QAWcNGu+7ZmTr/wDt7YYhW3kQDtYf1Z3tnP6ZnQOGtF+4ygQBEr4KAV9SZgFltsMCAQ4j+hsFQRBcW2C0/HoDhvx6AAABAF//7QT7BhkAJwCXshAoKRESOQCwAEVYsCIvG7EiIj5ZsABFWLARLxuxERw+WbAARViwHS8bsR0cPlmwAEVYsCYvG7EmHD5ZsABFWLAZLxuxGRA+WbAARViwCy8bsQsQPlmwJhCxAAGwCitYIdgb9FmwCxCxBgGwCitYIdgb9FmwABCwD9CwENCwIhCxFQGwCitYIdgb9FmwEBCwG9CwHNAwMQEjAwYXFhcWNwcGJyYmNxMjNzM3JiMiBgcDIxMjNzM3NjYXFhYXAzME27lmAwIGSSMyEUpKe3wNZa0grC9CY01nD8vtnaEgoA0Z16py22k6uQOG/aIZFEADAgq+FQECo4kCarT6Il1Y+18DhrRfuMgCAT8r/o4AAAEAF//pBp0GGgBKAMayKUtMERI5ALAARViwPi8bsT4cPlmwAEVYsEUvG7FFIj5ZsABFWLAQLxuxEBw+WbAARViwSS8bsUkcPlmwAEVYsCwvG7EsED5ZsABFWLAKLxuxChA+WbBJELEBAbAKK1gh2Bv0WbAKELEFAbAKK1gh2Bv0WbABELAO0LBFELEVB7AKK1gh2Bv0WbIdSSwREjmwPhCxIAGwCitYIdgb9FmyNyw+ERI5sDcQsSYBsAorWCHYG/RZsCwQsTMBsAorWCHYG/RZMDEBIwMHFhcWNwcGJyYmNxMjNzM3NiYnJgYfAhYHBzYmJyIGBwYEFxYHDgInJiY3MxQWFzI2NzYkJyY3NiQXMhcmNzY2FxYWBwczBn65ZAIDSyMyEUtKe3gPYKcfpg0KSk1dcwkEEwYE7gJSTE5zCw8BEETNCgV+1Xax5ALmY1ZadQwR/u4W+AgHAQWxS18TBg7rqLnFFQy5A4b9ti9SAwIKvhUBArSZAkm0WV9pAgOFjTyqOjkBS1YCTUFaRR1Xu2iZUQMCyZ9YWQJJQWBOCFjDlr4CGXw5iaUCBNasWAAAFv+p/nIIRQWuAA0AGgAoADcAPQBDAEkATwBWAFoAXgBiAGYAagBuAHYAegB+AIIAhgCKAI4BqbJbj5AREjmwWxCwDNCwWxCwGtCwWxCwHNCwWxCwMdCwWxCwPNCwWxCwPtCwWxCwRtCwWxCwStCwWxCwUtCwWxCwV9CwWxCwYdCwWxCwY9CwWxCwadCwWxCwbdCwWxCwcNCwWxCwetCwWxCwftCwWxCwgtCwWxCwhNCwWxCwiNCwWxCwjNAAsD0vsABFWLBGLxuxRiA+WbJ/OgMrsneCAyuye3oDK7JJfgMrsolOAyuyhYgDK7KNhAMrskGMAyuyCj1GERI5sAovsAPQsAMvsA7QsA4vsAoQsA/QsA8vsm8ODxESOXywby8YsVALsAorWCHYG/RZshVQbxESObAKELEeC7AKK1gh2Bv0WbADELElC7AKK1gh2Bv0WbAPELAp0LApL7AOELAu0LAuL7E0C7AKK1gh2Bv0WbA9ELBr0LBn0LBj0LA+0LE/DLAKK1gh2Bv0WbBl0LBp0LBt0LA80LBGELFHDLAKK1gh2Bv0WbBf0LBb0LBX0LBK0LBGELBg0LBc0LBY0LBL0LAOELFRC7AKK1gh2Bv0WbAPELF2C7AKK1gh2Bv0WTAxAQYGJyYmNzc2NhcWFgcTExcWBwYGBxYVFAYHATYmJyYGBwcGFhcWNjcBMwMGBiMGJicXBjcyNjcBEzMHMwchNzM3MwMBEyEHIwclNyEDIzcBBzM2NzYnATchByE3IQchNyEHEzchByE3IQchNyEHATc2NzYvAgEjNzM3IzczAyM3MyUjNzM3IzczAyM3MwMPCohgYXQECAiFZV11AgxgqL8DAiY4T21g/rUHNzo/VQsPBzg7P1QLA9BjOwhpT1NnAlgEVi06CflkN28kvxQE/xTAJG03+bUyAS0Uvh4F2xQBLzNtHvvoHm1uEg1RAUgVARAV/W0VAQ8V/W4VAQ4VzBQBDxT9bhQBDhT9bxQBDRQBV1Z6EApAI2D8znAtbxVvLHCvcC1vBwBtLG4UbSxur24tbQHUZnkCAn1ecGB+AgJ4Yv64AiUBBoknOCAdWElWAwFMQFACAlRDcUBRAgJRRQFP/oVNXQFTVQJfAjkq/MkBO8pxccr+xQYfAR10qal0/uOp/LapBVVHBwNLdHR0dHR0+ThxcXFxcXEDwgEGUTYIAwL+0fx++vwV+X78fvr8FfkAAAUAXP3VB9cIcwADABwAIAAkACgATACwIS+wJS+wANCwAC+wIRCwAtCwAi+yIAIAERI5sCAvsB3QsB0vsATQsAQvsg0AAhESObANL7AU0LAUL7IHBBQREjmyGRQEERI5MDEJAwU0Njc2NjU0JiMiBgczNjYzMhYVFAcGBhUXIxUzAzMVIwMzFSMEGAO//EH8RAQPHiRKXKeVkKACywI6Kzk4XVsvysrKSwQEAgQEBlL8MfwxA8/xOjoYJ4dKgJeLfzM0QDRfPEFcTFuq/UwECp4EAAH/4wAAAn4DJAAXAFuyCBgZERI5ALAARViwDy8bsQ8aPlmwAEVYsAAvG7EAED5ZsRYCsAorWCHYG/RZsgIWABESObIDDwAREjmwDxCxCAKwCitYIdgb9FmyDAAPERI5shUADxESOTAxISE3ATY3NiYnIgYHBzY2FxYWBwYPAgUCNv2tGAFWYQwHKyk6Qwy2Cq+Cf5IFBZZPnQFfhwEZU0MpLwFHNAF5mAICg2h+dzxuAgABAG0AAAINAxMABgAyALAARViwBS8bsQUaPlmwAEVYsAEvG7EBED5ZsAUQsATQsAQvsQMCsAorWCHYG/RZMDEhIxMHNyUzAYu1Y8wbAW4XAjYvmXMAAAIAF//wAowDJQANABkASLIRGhsREjmwERCwB9AAsABFWLAHLxuxBxo+WbAARViwAC8bsQAQPlmwBxCxEQKwCitYIdgb9FmwABCxFwKwCitYIdgb9FkwMQUmJjc3NjYXFhYHBwYGEzc0JyYPAhQXFjcBJYSKCxATsoiEiQsPErEdAlZ2FxYCWXYXDASwlo+osAQEspaPprAB8zdvAwO1sDBvAwfDAAACAGT/9QO9BKEAFgAkAFuyEyUmERI5sBMQsBfQALAARViwDC8bsQwePlmwAEVYsBQvG7EUED5ZsQABsAorWCHYG/RZsgUUDBESObAFL7EXAbAKK1gh2Bv0WbAMELEfAbAKK1gh2Bv0WTAxNxY2NwYnJiY3PgIXHgIHBwIABSM3ARY3NzYnJiYnJgYHBhbUtuM5bYegrg0JhNN4fKxMDQYj/oD+xhoUASp0WA4FBAdLP1Z9DAtQtgKPk10CAtKoeMZsAwJ61og5/sD+qwHCAXkEaFsoKUdRAQKAY1VxAAADACH/7QO+BKAAFgAiAC4AgbIhLzAREjmwIRCwCNCwIRCwJ9AAsABFWLAULxuxFB4+WbAARViwCS8bsQkQPlmyLBQJERI5sCwvss8sAXGyPywBcbJfLAFysRoBsAorWCHYG/RZsgIsGhESObIPGiwREjmwCRCxIAGwCitYIdgb9FmwFBCxJgGwCitYIdgb9FkwMQEGBxYWBw4CJy4CNzY3Jjc2NhcWFgE2JiMiBgcGFjMyNhM2JiMiBgcGFjMyNgO3CspJTwQGftJ5c7RgBgrqggYH77msxP7PCVpNWHgJCVhOV3pFCE1CSmgHCExBSGsDX6llKopWbJ1RAwJPk2LCZliXmboCBaz9bEVVXkpFVV0CQj9MV0I+UFYAAAEAWAAABBYEjQAGADqyAQcIERI5ALAARViwBS8bsQUePlmwAEVYsAEvG7EBED5ZsAUQsQMBsAorWCHYG/RZsgADBRESOTAxAQEhASE3IQP8/WP++QKb/YMjA30D+PwIA8nEAAACADz/6wOtBJsAFAAhAF6yEyIjERI5sBMQsBXQALAARViwAC8bsQAePlmwAEVYsA0vG7ENED5ZsAAQsQIBsAorWCHYG/RZsgcADRESObAHL7EVAbAKK1gh2Bv0WbANELEcAbAKK1gh2Bv0WTAxAQcnJgYHNhcWFgcGBCcmAjc3EgAlASYHBhcWFhcWNjc2JgNlFQuz+EF3lpmuDQ7+87y+zxUJKgGAATP+5YZZCwUJUEJXgQoLVASbyAECmKZxBALWpbbnBAQBAsxOAS8BWAX9qgRxVi5NVAECe1tUaQABADH/6gPcBI0AGwBtshkcHRESOQCwAEVYsAEvG7EBHj5ZsABFWLANLxuxDRA+WbABELEDAbAKK1gh2Bv0WbIHAQ0REjmwBy+xGQewCitYIdgb9FmyBRkHERI5sA0QsRMBsAorWCHYG/RZshETGRESObIbGRMREjkwMRMTIQchBzYzMhYHBgQnJiYnFxYXMjY3NiYjJgd9rgKxI/4YVVhopboMDv71wKfYBuUJo1x7CgpbV1tgAj8CTsj1K8+nueIEBLqWAYwCdl9VbAI+AAL//AAAA7QEjQAKAA4AV7IODxAREjmwDhCwCdAAsABFWLAJLxuxCR4+WbAARViwBC8bsQQQPlmyAQkEERI5sAEvsQIBsAorWCHYG/RZsAbQsggGARESObABELAL0LINCQQREjkwMQEzByMHIzchNwEzASETBwMflSKVLO0s/ewSApP7/W4BJ1AaAbzE+PinAu79LwGdIwAAAQAI/+0DwgSgACcAkrIiKCkREjkAsABFWLAOLxuxDh4+WbAARViwGi8bsRoQPlmyAQ4aERI5sAEvtK8BvwECXbRvAX8BAnGyPwEBcbIvAQFdsv8BAXGyDwEBcrAOELEHAbAKK1gh2Bv0WbILDhoREjmwARCxJgewCitYIdgb9FmyEyYBERI5sh0OGhESObAaELEhAbAKK1gh2Bv0WTAxARc2Njc2JiMmBgcHNjYXFhYHBgcWFgcOAicmJjcXBhYzFjY3NicnAWxwZn0KCVNLS28O7gr7uLnOCQrfVVIFBoHefLHYBOsCX1BZgQsV034CqQEDVk1DSwJGPwGUtAIFsJGtWyCHW2ygVQMCu5sBRU8CWk+dAgEAAAH/8QAAA78EoAAXAGCyAxgZERI5ALAARViwDy8bsQ8ePlmwAEVYsAAvG7EAED5ZsRcBsAorWCHYG/RZsgIXABESORmwAi8YsgMPABESObAPELEIAbAKK1gh2Bv0WbIMDwAREjmyFQAPERI5MDEhITcBNjc2JicmBgcHNiQXFhYHBgcHBQUDUvyfHgIRkREKSUdgeQ/sDQEKxLPJCwzZZv7fAi2vAbd5aEFTAgJoXAKu2QIFupettVLQAgAAAQCyAAADCwSNAAYAQLIBBwgREjkAsABFWLAFLxuxBR4+WbAARViwAC8bsQAQPlmwBRCwBNCwBC+xAwGwCitYIdgb9FmyAgMFERI5MDEhIxMFNyUzAkjumP7AJAIVIANrU8uqAAIAOv/rA9kEogANABwASLIaHR4REjmwGhCwANAAsABFWLAHLxuxBx4+WbAARViwAC8bsQAQPlmwBxCxEgGwCitYIdgb9FmwABCxGQGwCitYIdgb9FkwMQUmAjc3NgAXFhIHBwYAEzc0JicmAwMHFxYXFjY3Aba+vhkbIgEHxsC8GRwk/vxZBUlFvC4lBQELg1p7FhAFAQvhw/gBBgUF/vHew/z+/wLZW1ZkAgb+9v70WxeiBwWIkQAB/9YAAAQnBI0ACQBGALAARViwBy8bsQcePlmwAEVYsAIvG7ECED5ZsQABsAorWCHYG/RZsgQAAhESObAHELEFAbAKK1gh2Bv0WbIJBQcREjkwMSUhByE3ASE3IQcBMAJgI/xpGwLf/a8jA4UawsKYAzHElgAAAQBtAAAEgASNAAgAMQCwAEVYsAEvG7EBHj5ZsABFWLAHLxuxBx4+WbAARViwBC8bsQQQPlmyAAEEERI5MDEBASEBAyMTATMCDAFiARL93ETsS/729wJ8AhH8+v55Aa4C3wAB/6QAAASABI0ACwBTALAARViwAS8bsQEePlmwAEVYsAovG7EKHj5ZsABFWLAELxuxBBA+WbAARViwBy8bsQcQPlmyAAEEERI5sgYBBBESObIDAAYREjmyCQYAERI5MDEBASEBASEDASEBASECKwExAST+JQEV/vew/sf+3AHm/vwBBAL7AZL9sv3BAZj+aAJXAjYAAQCMAAAGHgSNAAwAWQCwAEVYsAEvG7EBHj5ZsABFWLAILxuxCB4+WbAARViwCy8bsQsePlmwAEVYsAMvG7EDED5ZsABFWLAGLxuxBhA+WbIAAQMREjmyBQEDERI5sgoBAxESOTAxAQEzASMDASMDMxMBMwPyAUDs/iTlQP6c5kfgFAFn0QEuA1/7cwM+/MIEjfyhA18AAQBwAAAEuASOAAgAMQCwAEVYsAMvG7EDHj5ZsABFWLAHLxuxBx4+WbAARViwBS8bsQUQPlmyAQMFERI5MDEBFzcBIQEjAzcB5AUjAagBBP2J8OHqAThKUwNM+3MEjQEAAQA5/+sEagSNABEAPbIOEhMREjkAsABFWLAALxuxAB4+WbAARViwCC8bsQgePlmwAEVYsAQvG7EEED5ZsQ0BsAorWCHYG/RZMDEBAwYEJyYmNxMzAwYWFxY2NxMEaoAb/uXSyeAUgeyCC1tna44SgwSN/QHC4QQE5bUDAPz/ZXIDBG9pAwcAAAEAYgAABFoEjQAHAC8AsABFWLAGLxuxBh4+WbAARViwAi8bsQIQPlmwBhCxAAGwCitYIdgb9FmwBNAwMQEhAyMTITchBDf+iqjtqP6OIwPVA8n8NwPJxAABAA7/7QP/BJ8AJgBwshEnKBESOQCwAEVYsAkvG7EJHj5ZsABFWLAcLxuxHBA+WbICHAkREjmyDAkcERI5sgwMAV2wCRCxEAGwCitYIdgb9FmwAhCxFQGwCitYIdgb9FmyIAkcERI5sgMgAV2wHBCxJAGwCitYIdgb9FkwMQE2LwImNzYkFxYWByc2JiciBgcGBBcWBw4CJyYnJjcXBhYzMjYCuxGPdkf9DQkBC7+84ALrA2dUXXsMEQE9RsQKB3/YgJ5ypgTsBW1uYXsBOWovJBpk1Ju8AgXCogFWVgFQQ2FdJWfGbJdPAwJHaMgBXWJNAAIACgAABBYEjQANABUAYLIAFhcREjmwD9AAsABFWLAELxuxBB4+WbAARViwAi8bsQIQPlmwAEVYsAwvG7EMED5Zsg8EAhESObAPL7EAAbAKK1gh2Bv0WbIKAA8REjmwBBCxFQGwCitYIdgb9FkwMQEjAyMTBRYWBwYFExUjARc2Njc2JycCH95K7coBrMXRCg//ALn8/qjDaIYMFrrcAan+VwSNAQW3m/Bh/ikNAmsCAmBVnwkBAAACADf/MARgBKMAEwAiAEiyAyMkERI5sAMQsB/QALAARViwDS8bsQ0ePlmwAEVYsAUvG7EFED5ZsA0QsRcBsAorWCHYG/RZsAUQsR4BsAorWCHYG/RZMDElFwcnBiMmJgI3NxIAFxYWEgcHAgMmJicmAgcVFhYXFjY3NgMqr6XdOiiRz14RAyABSe2Tz10RBy6yB2ximb8KBWxigLQfFkyefsgHApUBBp4bAREBSwYEkv75oTr+vwICb4AEBv785khxhgQFt6p3AAIACgAABDYEjQAKABMAT7IEFBUREjmwBBCwDNAAsABFWLADLxuxAx4+WbAARViwAS8bsQEQPlmyCwEDERI5sAsvsQABsAorWCHYG/RZsAMQsRIBsAorWCHYG/RZMDEBAyMTBRYWBwYEIycXMjY3NiYnJwE+R+3KAci83gsK/u3X191rjAwLXFj4AZn+ZwSNAQTQpa/MxQFgVVJhBAEAAgA6/+oEYwShABAAIABIsh4hIhESObAeELAI0ACwAEVYsAkvG7EJHj5ZsABFWLAALxuxABA+WbAJELEWAbAKK1gh2Bv0WbAAELEdAbAKK1gh2Bv0WTAxBSYmAjc3NhI2FxYWEgcHAgATNicmJicmAgcXFhYXFjY3AfuT0V0RCRil/JiTzl0RAyD+uX4GAwVrYprACQEFbWGHuBkQBJUBA51DpQEFiwQEkv77nBz+6f63An49QG6CBAb++uVIcYUEBc6/AAEACgAABKgEjQAJAEUAsABFWLAFLxuxBR4+WbAARViwCC8bsQgePlmwAEVYsAAvG7EAED5ZsABFWLADLxuxAxA+WbICBQAREjmyBwUAERI5MDEhIwEDIxMzARMzA97k/omM7crlAXeM7AMl/NsEjfzaAyYAAQAKAAAFyASNAA4AYLIBDxAREjkAsABFWLAALxuxAB4+WbAARViwAi8bsQIePlmwAEVYsAQvG7EEED5ZsABFWLAILxuxCBA+WbAARViwDC8bsQwQPlmyAQAEERI5sgcABBESObIKAAQREjkwMQETASEDIxMTASMLAiMTAgO0AdUBPMvsOXT+HaW+TTXsygSN/LcDSftzAUgCF/yhA3z9sv7SBI0AAAEACgAAAzQEjQAFACkAsABFWLAELxuxBB4+WbAARViwAi8bsQIQPlmxAAGwCitYIdgb9FkwMSUhByETMwEZAhsj/PnK7cLCBI0AAQAKAAAEnQSNAAwASwCwAEVYsAQvG7EEHj5ZsABFWLAILxuxCB4+WbAARViwAi8bsQIQPlmwAEVYsAsvG7ELED5ZsgYCBBESObAGELAB0LIKAQYREjkwMQEHAyMTMwM3ASEBASEB1aQ67crtV3wBgAE3/eoBUP72AdmL/rIEjf4LfgF3/ez9hwAAAf/y/+sDsASNAA4AMLIFDxAREjkAsABFWLAALxuxAB4+WbAARViwBS8bsQUQPlmxCwGwCitYIdgb9FkwMQEzAwYGJyYmNxcGFxY2NwLD7YYZ9621xgbtCZ9KaA8Ejfzgs88EBMOqAasEAmNbAAABABgAAAHPBI0AAwAdALAARViwAi8bsQIePlmwAEVYsAAvG7EAED5ZMDEhIxMzAQXty+wEjQABAAoAAASpBI0ACwCHALAARViwBi8bsQYePlmwAEVYsAovG7EKHj5ZsABFWLAALxuxABA+WbAARViwBC8bsQQQPlmyCQYAERI5sAkvtK8JvwkCXbI/CQFxss8JAXGyPwkBcrL/CQFxsg8JAXK0bwl/CQJxtN8J7wkCXbJfCQFytBwJLAkCXbECAbAKK1gh2Bv0WTAxISMTIQMjEzMDIRMzA9/tUv4GU+3K7VYB+1btAdv+JQSN/hEB7wABAD//8ARRBKMAIABnsgIhIhESOQCwAEVYsAsvG7ELHj5ZsABFWLADLxuxAxA+WbIfCwMREjmwHy+wCxCxEQGwCitYIdgb9FmyDx8RERI5sgwPAV2wAxCxGgGwCitYIdgb9FmwHxCxHQGwCitYIdgb9FkwMSUGBQcuAjc3EgAXFhYXJyYnJgYHBwYXFhYXFjc3IzchA+d//to6ldRgEQYfAUHtwd0Q5BK9hrUbDAcFCHRmh1oo8yAB3ZKUDQECkP+eNwERATwGBMm4AbwGBbuqWkFBbnsDAjrIsQAAAQAKAAAD5gSNAAkARwCwAEVYsAQvG7EEHj5ZsABFWLACLxuxAhA+WbIJBAIREjmwCS+ySgkBXbEAAbAKK1gh2Bv0WbAEELEGAbAKK1gh2Bv0WTAxASEDIxMhByEDIQMs/h5T7coDEiP93DQB5AHb/iUEjcT+1QAAAQAO/xMD/wVzACsAcrIfLC0REjkAsABFWLAJLxuxCR4+WbAARViwIi8bsSIQPlmyAyIJERI5sAkQsAzQsAMQsRkBsAorWCHYG/RZsAkQsRMBsAorWCHYG/RZshAZExESObAiELAf0LAiELEpAbAKK1gh2Bv0WbIlAykREjkwMQE2JycmJjc2Njc3MwcWFgcnNiYnIgYHBhcXFhYHBgYHByM3JiY3FwYWMzI2ArsRjzzMrAcJ47MsnS2RowLrA2ZVXXsMEZ0+yKEICdq0LpwupLwE7AVubmB7ATlqLxI4rX6OtBHZ3xu7igFWVwFQQ2AwEj2zgI6rEeHjGMeUAV1iTQAAAQARAAAECQSiAB8AaLIbICEREjkAsABFWLAULxuxFB4+WbAARViwBi8bsQYQPlmyHwYUERI5sB8vsA/QsQ4CsAorWCHYG/RZsADQsAYQsQUBsAorWCHYG/RZsAjQsBQQsRoBsAorWCHYG/RZshcfGhESOTAxASUGBgclByE3FzY/Agc3Mzc2NhcWFgcnNicmBgcHIQMb/pgROzoCiST8fx0IXSINA6UclgwY8bitvQjuC49SZw0KAXYB5QFUkkADw8IBJa9HDgWTaNPvBATWuAHGBwKEfmIAAQADAAAD5gSNABgAb7IAGRoREjkAsABFWLABLxuxAR4+WbAARViwDC8bsQwQPlmyAAwBERI5sggBDBESObAIL7AD0HywAy8YsAWwCitY2BvcWbAIELAKsAorWNgb3FmwDtCwCBCwENCwBRCwE9CwAxCwFdCwARCwF9AwMQEBIQEzByMHByEHIQcjNyE3Mzc1ITczAzMByAEaAQT+nbMa9BMMAQga/v4n7Cb++xv+FP74G8aZ8AKQAf39t5EZMZHY2JE/C5ECSQABAAoAAAPfBI0ABQAzsgEGBxESOQCwAEVYsAQvG7EEHj5ZsABFWLACLxuxAhA+WbAEELEAAbAKK1gh2Bv0WTAxASEDIxMhA7z946jtygMLA8n8NwSNAAL/pwAAA+wEjQADAAgAPbIFCQoREjmwBRCwAtAAsABFWLACLxuxAh4+WbAARViwAC8bsQAQPlmyBQACERI5sQcBsAorWCHYG/RZMDEhIQEzAycHASED7Pu7AnbtogUc/q8B1wSN/rpeRP1iAAMAOv/qBGMEogADABQAIgB0shgjJBESObAYELAC0LAYELAN0ACwAEVYsA0vG7ENHj5ZsABFWLAELxuxBBA+WbIDDQQREjl8sAMvGLRgA3ADAl20MANAAwJdsQABsAorWCHYG/RZsA0QsRgBsAorWCHYG/RZsAQQsR8BsAorWCHYG/RZMDEBITchASYmAjc3Ejc2FxYWEgcHAgATJiYnJgIHFxYWFxYSNwMQ/mUjAZv+yZPRXhEDIbGh5JPOXREEIP65gwVsYprACQEFbGKXwAsB38P9TgKVAQSeHAEdqJgFBJL+/J4h/u3+uQL7bYMEBv786EdxhQQGAQDwAAH/pwAAA+wEjQAIADiyBwkKERI5ALAARViwAi8bsQIePlmwAEVYsAAvG7EAED5ZsABFWLAELxuxBBA+WbIHAgAREjkwMTMjATMTIQMnB5HqAnbt4v7/gwUiBI37cwNHXlEAA//XAAADnwSNAAMABwALAGGyBAwNERI5sAQQsADQsAQQsAjQALAARViwCi8bsQoePlmwAEVYsAAvG7EAED5ZsQIBsAorWCHYG/RZsgcKABESObAHL7EEAbAKK1gh2Bv0WbAKELEIAbAKK1gh2Bv0WTAxISE3IQMhNyETITchAtT9AyMC/RL9kCMCcHT9AyMC/cMBOMQBCsQAAAEACgAABKQEjQAHAECyAQgJERI5ALAARViwBi8bsQYePlmwAEVYsAQvG7EEED5ZsABFWLABLxuxARA+WbAGELECAbAKK1gh2Bv0WTAxISMTIQMjEyED2e6o/gyo7coD0APJ/DcEjQAAAf/ZAAAEBwSNAAwATbIADQ4REjkAsABFWLAILxuxCB4+WbAARViwAy8bsQMQPlmxAQGwCitYIdgb9FmyBQEDERI5sAgQsQoBsAorWCHYG/RZsgcKCBESOTAxAQEhByE3AQM3IQchEwJ7/rMCViP8eB0Bgu0ZA2Mj/cPVAkT+gMSkAbcBpozE/pAAAwBDAAAFNwSOABEAFwAdAG6yEB4fERI5sBAQsBXQsBAQsBvQALAARViwEC8bsRAePlmwAEVYsAcvG7EHED5Zsg8QBxESObAPL7AA0LIGBxAREjmwBi+wCdCxFAGwCitYIdgb9FmwDxCxFQGwCitYIdgb9FmwGtCwFBCwG9AwMQEWFgcGAAcHIzcmJjc2JDc3FwEGFxMGBgU2JwM2NgN+0OkPEP7K+RjuGdHoDxABOPcb7f2kH/Jqj54C7xvta4ujBBMU9bzR/wAQbW4T+sHP/A55Af2v7yICLhCTZ+ch/dIPlwAAAQBwAAAFUQSNABkAXbIYGhsREjkAsABFWLAELxuxBB4+WbAARViwEC8bsRAePlmwAEVYsBgvG7EYHj5ZsABFWLAKLxuxChA+WbIXBAoREjmwFy+wANCwFxCxDAGwCitYIdgb9FmwCdAwMQE2NjcTMwMGAAcDIxMmAjcTMwMGBwYWFxMzAwF6mRwz7jUp/t3kN+44y8QeMu0yCAEDUVR+7QHaGrmqATb+xfz+2xj+5wEZHQE57wEv/tA5PGmKGAKwAAABAAAAAAR4BKEAJABbsgAlJhESOQCwAEVYsBovG7EaHj5ZsABFWLAQLxuxEBA+WbAARViwIy8bsSMQPlmxIQGwCitYIdgb9FmwANCwGhCxCAGwCitYIdgb9FmwABCwD9CwIRCwEtAwMSU2Njc2JyYmJyYGBgcXFhcHITc3Jjc3PgIXHgIHBwIHNwchAk58lRkMBgxvYGmgVAMBDJIe/jwkqYEXBRKl/pON1GcNBSPgtCP+PMclyLFoPGJrAwNt0LckwzjJxAK3+iuS7n8EA4Pojyv+55wExAABAGL/6wUNBI0AFwBusgUYGRESOQCwAEVYsAIvG7ECHj5ZsABFWLAWLxuxFhA+WbAARViwDi8bsQ4QPlmwAhCxAAGwCitYIdgb9FmwBNCwBdCyCAIWERI5sAgvsA4QsQ8HsAorWCHYG/RZsAgQsRMBsAorWCHYG/RZMDEBITchByEHNhcWFgcGBAc3Njc2JyYHAyMBsv6wIwOSI/6sMoSIwNMMDv728hTwGRrOZ59j7QPJxMTvKQMC1bm8xwK9BcHKBgMn/eYAAAEAOf/sBEkEowAeAIeyHB8gERI5ALAARViwCy8bsQsePlmwAEVYsAMvG7EDED5ZsgALAxESObIOCwMREjmwCxCxEgGwCitYIdgb9FmyFQsDERI5fLAVLxiy8BUBXbIAFQFxtDAVQBUCXbSAFZAVAnG0YBVwFQJdsRYBsAorWCHYG/RZsAMQsRwBsAorWCHYG/RZMDEBBgQnLgI3NxIAFxYWFycmJicmAyEHIQYXFhYXFjcD/Bz+39SQyVkSBiABQerB4grrA2Br7VwBfSL+kgYFB2VX4zkBhb3cBAKQ/580AQ4BQQYE3b0BZ3AEB/7HxDg2W2gDCNoAAv/B//8GxASNABgAIQBusgUiIxESObAFELAa0ACwAEVYsBMvG7ETHj5ZsABFWLADLxuxAxA+WbAARViwCy8bsQsQPlmwExCxBQGwCitYIdgb9FmyFhMDERI5sBYvsAMQsRsBsAorWCHYG/RZsBYQsSEBsAorWCHYG/RZMDEBBgQjIRMhAwcCBicjNzc2Njc3EyEDFxYWJQMXNjY3NiYnBrkL/u3a/h6p/rBEGTvnuj4YImZ8Hw9oAyRGx8bm/WtB3GaPDQtYWQGHr9gDyf62f/7s7QHMAQakwFwB+v5sAQHKCP6OAQJrWkxaBQACAAoAAAbHBI0AEgAbAIWyAhwdERI5sAIQsBTQALAARViwAi8bsQIePlmwAEVYsBEvG7ERHj5ZsABFWLALLxuxCxA+WbAARViwDy8bsQ8QPlmyAQILERI5sAEvsAIQsBuwCitY2BvcWbEFAbAKK1gh2Bv0WbABELENAbAKK1gh2Bv0WbALELEVAbAKK1gh2Bv0WTAxASETMwMXFhYHBgQjIRMhAyMTMwEDFzY2NzYmJwFrAexW7kfJxeULC/7t2P4dU/4UU+3K7QJyQdxojQ0LWFkCngHv/mwBAcqmr9gB2/4lBI39qP6OAQJrWkxaBQABAGIAAAUOBI0AFgBcsgUXGBESOQCwAEVYsAIvG7ECHj5ZsABFWLAMLxuxDBA+WbAARViwFS8bsRUQPlmwAhCxAAGwCitYIdgb9FmwBNCwBdCyCAIMERI5sAgvsRIBsAorWCHYG/RZMDEBITchByEHNhcWFgcDIxM2JyYnJgcDIwGy/rAjA5Ij/qwygYrJzBQ47TkGBRObbJtj7QPJxMTuJwIE4ML+pgFbNCl/BgMm/eYAAQAK/p8EowSNAAsAULIDDA0REjkAsAIvsABFWLAGLxuxBh4+WbAARViwCi8bsQoePlmwAEVYsAAvG7EAED5ZsABFWLAELxuxBBA+WbEIAbAKK1gh2Bv0WbAJ0DAxISEDIxMhEzMDIRMzA9j+lj7tPv6Jyu2oAfSo7v6fAWEEjfw2A8oAAgAL//wD9wSNAA0AFgBhshQXGBESObAUELAJ0ACwAEVYsAwvG7EMHj5ZsABFWLALLxuxCxA+WbAMELEAAbAKK1gh2Bv0WbIDDAsREjmwAy+wCxCxDgGwCitYIdgb9FmwAxCxFAGwCitYIdgb9FkwMQEhBxcWFgcOAiclEyEBNjY3NCYnJwMD1f3JJ/nAxRUQkueF/jnLAyH+GWh8Amlc3D4Dy+ABBcOid7FcAwEEjfw1AmZXTFcCAf6cAAAC/4P+rwTABI0ADgAUAFmyEhUWERI5sBIQsAnQALAARViwBC8bsQQePlmwAEVYsAovG7EKED5ZsQABsAorWCHYG/RZsAywCitY2BvcWbAI0LIPBAoREjmwBBCxEQGwCitYIdgb9FkwMTc2NjcTIQMzAyMTIQMjEwUlEyEDAjFvgyRSAyepklztO/0QO+1dAWcB44b+rkBBwGX9xQGm/Db97AFR/q8CEwMEAwb+uP7cAAH/qQAABjsEjQAVAJ+yARYXERI5ALAARViwES8bsREePlmwAEVYsA4vG7EOHj5ZsABFWLAKLxuxCh4+WbAARViwBi8bsQYQPlmwAEVYsAMvG7EDED5ZsABFWLAVLxuxFRA+WbIMAw4REjmwDC+yPwwBcbJfDAFyss8MAXG0rwy/DAJdtI8MnwwCcrAP0LEBAbAKK1gh2Bv0WbAE0LIIDwQREjmyEwEPERI5MDEBIwMjEyMBIQEDIRMzEzMDMwEhARMhA8pmUe1SVf66/swBw8sBCZxXU+5USQFEAST+Yeb+7gHV/isB1f4rAmECLP4gAeD+IAHg/cP9sAABAAz/7gPvBKAAJgBDsiAnKBESOQCwAC+wAEVYsBgvG7EYED5ZsgkAGBESObIMABgREjmxHwGwCitYIdgb9FmwABCxJAewCitYIdgb9FkwMQEyNjc2JiIGBwc2NhcWFgcGBxYWBw4CJyYmNzMWFjMWNjc2Jyc3AgVmgAoKZbBqD+4M/cLD3ggK6VFaBAV87Iu53gTqAlxWapAMFdyHIAKqU01ETEU+AZiyAgOmjbVlI4ZZap1XAgK5nEdMA1lPoAEBsAAAAQALAAAErgSNAAkATLIACgsREjkAsABFWLAALxuxAB4+WbAARViwCC8bsQgePlmwAEVYsAUvG7EFED5ZsABFWLADLxuxAxA+WbIEAwAREjmyCQUIERI5MDEBMwMjEwEjEzMDA8vjy+qP/Wbjy+qPBI37cwMx/M8EjfzSAAEACgAABG0EjQAMAHiyAA0OERI5ALAARViwCC8bsQgePlmwAEVYsAUvG7EFHj5ZsABFWLACLxuxAhA+WbAARViwDC8bsQwQPlmyBgIFERI5sAYvsj8GAXGyXwYBcrLPBgFxtK8GvwYCXbSPBp8GAnKxAQGwCitYIdgb9FmyCgEGERI5MDEBIwMjEzMDMwEhAQEhAbZtUu3K7VRXAYMBJv4QATP+6QHV/isEjf4gAeD9uf26AAH/wQAABJcEjQARAECyBBITERI5ALAARViwAC8bsQAePlmwAEVYsAEvG7EBED5ZsABFWLAJLxuxCRA+WbAAELEDAbAKK1gh2Bv0WTAxAQMjEyEDBwIGByM3NzY2NzcTBJfK7qn+sUYZPOK0RxgkZ3scD2kEjftzA8n+tn3+7e0CzAMKqbhZAfoAAAEAcv/oBIIEjgAPAE+yARARERI5ALAHL7AARViwDy8bsQ8ePlmwAEVYsAgvG7EIED5ZsgEPCBESObICDwgREjmwAi+wCBCwCrAKK1jYG9xZsg4PCBESObAOLzAxARcBIQEGBiMnNxc2NjcDNwIQBwFcAQ/93VyydGsRUjpOI/r1Ako4Anv8dKN2BcQGATorA3wBAAABAAr+rwS4BI0ACwBDsgkMDRESOQCwAy+wAEVYsAcvG7EHHj5ZsABFWLAKLxuxCh4+WbAARViwBS8bsQUQPlmxCAGwCitYIdgb9FmwANAwMSUzAyMTIRMzAyETMwP7vXDYO/xfyu2oAfSo78P97AFRBI38NgPKAAABAF0AAARkBI0AEgBHsg4TFBESOQCwAEVYsAgvG7EIHj5ZsABFWLARLxuxER4+WbAARViwAC8bsQAQPlmyDggAERI5sA4vsQQBsAorWCHYG/RZMDEhIxMGJyYmNxMzAwYXFhcWNxMzA5ruUn9/0NMVOO46BgYTm2+YZO0BqycCAuDEAWH+njQpgAMDJQIgAAABAAoAAAZDBI0ACwBCsgcMDRESOQCwAEVYsAMvG7EDHj5ZsABFWLABLxuxARA+WbEEAbAKK1gh2Bv0WbADELAG0LAEELAI0LAGELAK0DAxISETMwMhEzMDIRMzBXj6ksrtqAFTqO6pAVSo7gSN/DYDyvw2A8oAAQAK/q8GWASNAA8AQrILEBEREjkAsAMvsABFWLAHLxuxBx4+WbAARViwBC8bsQQQPlmxAAGwCitYIdgb9FmwDdCwCdCwBxCwCtCwDtAwMSUzAyMTIRMzAyETMwMhEzMFm71w2Dv6v8rtqAFTqO6pAVSo78P97AFRBI38NgPK/DYDygAAAgBK//sE4wSNAAwAFQBhsgsWFxESObALELAU0ACwAEVYsAovG7EKHj5ZsABFWLAHLxuxBxA+WbIACgcREjmwAC+wChCxCAGwCitYIdgb9FmwBxCxDQGwCitYIdgb9FmwABCxEwGwCitYIdgb9FkwMQEWFgcGBCclEyE3IQMTNjY3NiYnJwMDXrvKFhj+1cz+OKj+rCMCPkaXZX8CAm1Y20EC+AXKorPZBAEDycT+bP3JAmtZTlwCAf6OAP//AAv/+wXhBI0AJgIIAAAABwHjBBIAAAACAAv/+wP3BI0ACgATAFKyERQVERI5sBEQsADQALAARViwCC8bsQgePlmwAEVYsAcvG7EHED5ZsAgQsBGwCitY2BvcWbEAAbAKK1gh2Bv0WbAHELELAbAKK1gh2Bv0WTAxARYWBwYEJyUTMwMTNjY3NiYnJwMCcrvKFhj+1cv+OMvqR5djggICbFrbQQL4Bcmjs9kEAQSN/mz9yQJrWU1dAgH+jgAAAQAT/+oEHgShAB0AhLILHh8REjkAsABFWLASLxuxEh4+WbAARViwGi8bsRoQPlmyABoSERI5sQMBsAorWCHYG/RZsggSGhESOXywCC8YtGAIcAgCXbQwCEAIAl2y8AgBXbIACAFxtIAIkAgCcbEFAbAKK1gh2Bv0WbASELELAbAKK1gh2Bv0WbIPEhoREjkwMRMWFhcWEyE3ITYmJyYGBwc2JBcWEg8CAgAnJiYn/QVlbO5W/oIjAW4NaW1xjBruIAEg0MroCAQGIf7D58PpCAGFamcDBwE7xI+gAwRzagG+4gQD/uvjNzP+8P7CBgTYuQACAAr/6wYiBKIAFgAjAJmyASQlERI5sAEQsB/QALAARViwDi8bsQ4ePlmwAEVYsAkvG7EJHj5ZsABFWLAGLxuxBhA+WbAARViwAC8bsQAQPlmyCgYJERI5fLAKLxi0YApwCgJdsvAKAV2yAAoBcbQwCkAKAl20gAqQCgJxsQUBsAorWCHYG/RZsA4QsRoBsAorWCHYG/RZsAAQsSABsAorWCHYG/RZMDEFLgI3BwMjEzMDMzYAFxYWEgcHBgIEEzQmJyYCBwYWFxYSNwO6h89nC75U7MrsVaxFATXSlM5dEQQVoP7/02tpncQCA2tsm78IEQSD5IkB/h4Ejf4Y9AEJBQST/v2eJLL+8JQC0oiQBAb+7/eGmwQGAQzuAAL/0gAABFYEjgANABYAY7IRFxgREjmwERCwDNAAsABFWLAHLxuxBx4+WbAARViwAC8bsQAQPlmwAEVYsAkvG7EJED5ZshIHABESObASL7ELAbAKK1gh2Bv0WbIBCxIREjmwBxCxEwGwCitYIdgb9FkwMSMBJiY3NiQzBQMjEyMBEwYWFxcTJyIGLgFyUlIGCQEHzwHRyu5O4v7UsQtVUeM6yV+DAg8rkV6nvgH7cwG8/kQDG0pPAgEBSgFbAAAB//UAAAREBI0ADQBSsgEODxESOQCwAEVYsAgvG7EIHj5ZsABFWLACLxuxAhA+WbIHAggREjmwBy+xBAewCitYIdgb9FmwAdCwCBCxCwGwCitYIdgb9FmwBxCwDNAwMQEjAyMTIzczEyEHIQMzAoDPVe1Uzh7NWQMLI/3jNtAB5v4aAeaqAf3E/scAAAH/qf6vBjsEjQAZAKyyCBobERI5ALADL7AARViwES8bsREePlmwAEVYsAUvG7EFED5ZsABFWLAJLxuxCRA+WbAARViwDS8bsQ0QPlmyFwkRERI5sBcvsj8XAXGyXxcBcrLPFwFxtK8XvxcCXbSPF58XAnKxBwGwCitYIdgb9FmyAAcXERI5sAUQsQEBsAorWCHYG/RZsAcQsAvQsg8XBxESObAXELAS0LARELAU0LAUL7AY0LAYLzAxARMzAyMTIwMjAyMTIwEhAQMhEzMTMwMzASEEnJvAXcs7n6VhUu1SVf66/swBw8sBCZxXU+5USQFEASQCUP5y/e0BUQHV/isB1f4rAmECLP4gAeD+IAHgAAABAAr+rwRtBI0AEACKsgAREhESOQCwBC+wAEVYsAwvG7EMHj5ZsABFWLAPLxuxDx4+WbAARViwCS8bsQkQPlmwAEVYsAYvG7EGED5Zsg0JDBESObANL7I/DQFxsl8NAXKyzw0BcbSvDb8NAl20jw2fDQJysQgBsAorWCHYG/RZsgAIDRESObAGELEBAbAKK1gh2Bv0WTAxARMzAyMTIwMjAyMTMwMzASECfc3LXcs7j+NtUu3K7VRXAYMBJgJG/nz97QFRAdX+KwSN/iAB4AAAAQAKAAAFJASNABQAgbIFFRYREjkAsABFWLAULxuxFB4+WbAARViwBi8bsQYePlmwAEVYsBEvG7ERED5ZsABFWLAKLxuxChA+WbIAERQREjmwAC+yPwABcbJfAAFyss8AAXG0rwC/AAJdtI8AnwACcrAE0LAAELEQAbAKK1gh2Bv0WbAM0LIIDAAREjkwMQEzNzMHNwEhAQEhAycHIzcjAyMTMwFpRCugLjIBgwEl/hABNP7q4j8poClEVu3K5gKr4OABAeH9uP27AdUBzM3+KQSNAAABAGIAAAVyBI0ADgCHsgkPEBESOQCwAEVYsAcvG7EHHj5ZsABFWLAKLxuxCh4+WbAARViwAi8bsQIQPlmwAEVYsA4vG7EOED5ZsggCBxESObAIL7I/CAFxsl8IAXKyzwgBcbSvCL8IAl20jwifCAJysQEBsAorWCHYG/RZsAcQsQQBsAorWCHYG/RZsgwBCBESOTAxASMDIxMhNyEDMwEFAQEhArxtUu2o/qojAkJUVwGCASb+EQEz/ukB1f4rA8rD/iAB4AH9uf27AAACAED/6gV5BKkAJAAvAIayAzAxERI5sAMQsC/QALAARViwCy8bsQsePlmwAEVYsBsvG7EbHj5ZsABFWLAELxuxBBA+WbAA0LICBBsREjmwAi+wCxCxDAGwCitYIdgb9FmwBBCxEwGwCitYIdgb9FmwABCxJAGwCitYIdgb9FmwAhCwJ9CwGxCxLAGwCitYIdgb9FkwMQUmJwYnJAATNxIANwcGBgcHBhYXNyYmNzc2EhcWFhcWBwYHFjMBFhc2Nzc2JyYDBgUc252imP71/uEbAxwBLucWeJsaBhWepD9ILwwFHvu5nbEJBBEjx2dI/foDf7QgDQyHuicJEgczPgICAUcBEx4BCAE1BM0Cs64rwtACA2nhfibxAQ8FBMmtT3j5sQcBZbNcfvKO0AUG/sxhAP//AG0AAASABI0AJgHTAAAABwImAAX+1QAB/6T+rwSABI0ADwBbsgoQERESOQCwBy+wAEVYsAEvG7EBHj5ZsABFWLAPLxuxDx4+WbAARViwCy8bsQsQPlmwAEVYsAkvG7EJED5ZsgAPCxESObEEAbAKK1gh2Bv0WbIKCw8REjkwMQEBIQETMwMjEyMDASEBASECKwExAST+JbjGXMs7hrD+x/7cAeb+/AEEAvsBkv2y/oP97QFRAZj+aAJXAjYAAQBi/q8FugSNAA8AXrIJEBEREjkAsAIvsABFWLAILxuxCB4+WbAARViwDi8bsQ4ePlmwAEVYsAQvG7EEED5ZsQABsAorWCHYG/RZsAgQsQYBsAorWCHYG/RZsArQsAvQsAAQsAzQsA3QMDElMwMjEyETITchByEDIRMzBPu/cNk7/GCo/q4jA4ci/raGAfWo7cP97AFRA8nExPz6A8oAAAEAXQAABGQEjQAYAFCyBRkaERI5ALAARViwCy8bsQsePlmwAEVYsBcvG7EXHj5ZsABFWLAALxuxABA+WbIRCwAREjmwES+xBwGwCitYIdgb9FmwBNCwERCwFNAwMSEjEwYHByM3JiY3EzMDBhcWFzczBzY3EzMDmu5RRlwqnyqvsBQ57joHAgN1MZ8vRF1k7QGrFQvNyhLctgFh/qQrKHgb9PMKFwIgAAEACgAABBEEjQASAEeyDhMUERI5ALAARViwAC8bsQAePlmwAEVYsAgvG7EIED5ZsABFWLARLxuxERA+WbIEAAgREjmwBC+xDgGwCitYIdgb9FkwMRMzAzYXFhYHAyMTNicmJyYHAyPU7VGEeNDVFTntOgYGE5tsm2TtBI3+VScCAuHD/p8BYjQpfwYDJv3fAAIAN//xBaUEpwAbACQAZ7IOJSYREjmwDhCwHdAAsABFWLAPLxuxDx4+WbAARViwAC8bsQAQPlmyIA8AERI5sCAvsRMBsAorWCHYG/RZsATQsCAQsAzQsAAQsRcBsAorWCHYG/RZsA8QsRwBsAorWCHYG/RZMDEFLgI3JiY3FwYXFhc2ABcWEgcHIQYWFxY3FwYDJgYHITYnJiYDWJrydRCXmQu8AwMHcz0BQtnm7x0X/N4SkpGBqS93fX23LQI6EQsPdA8Bg+eREtu1ASckeBvoAQ8EBP7Y9JmOngIDP71KA+4Dn5dTN05YAAIANP/sBHoEogAVAB8AYbIRICEREjmwERCwF9AAsABFWLAALxuxAB4+WbAARViwCC8bsQgQPlmyDgAIERI5sA4vsAAQsREBsAorWCHYG/RZsAgQsRYBsAorWCHYG/RZsA4QsRkBsAorWCHYG/RZMDEBHgIHBwYAJy4CNzchNiYnJgcnNhMWNjchBwYXFhYCg5/rbRENIP6r55nXXBMYAyASko+AqzB6fHy3Lf3HBgsKEHUEogOK+Jxl+/7LBAOJ9Z+ZkZsCAz+8S/wSA5+XGT0zUFcAAQAM/+cEBQSNABoAbbITGxwREjkAsABFWLACLxuxAh4+WbAARViwDC8bsQwQPlmwAhCxAAGwCitYIdgb9FmyBAACERI5shoMAhESObAaL7EYAbAKK1gh2Bv0WbIFGBoREjmwDBCxEgGwCitYIdgb9FmyEBIYERI5MDEBITchBwEWFgcOAicmJjczFhcWNjc2JicnNwKx/fgiAzob/paJnggHhuiIvNoE6gS1bIwKCl9gkSIDycSl/sUXuYF1p1kDBbyclAUCYlRNVwMBxQADADr/7ARjBKMAEAAXAB4Ai7IZHyAREjmwGRCwENCwGRCwEtAAsABFWLAILxuxCB4+WbAARViwAC8bsQAQPlmwCBCxEQGwCitYIdgb9FmyFQgAERI5fLAVLxiyMBUBXbJDFQFdtGAVcBUCXbLwFQFdsgAVAXG0gBWQFQJxsAAQsRgBsAorWCHYG/RZsBUQsRsBsAorWCHYG/RZMDEFJiYCNzcSABcWFhIHBwYCBBEmBgchNiYDFjY3IQYWAfuS0V4RAx8BSe+Rz14RBBWg/v9yrTMCJQpv/3OrMv3cCnAQApUBBJ4cAREBTQYCkv76niSy/vGUA+0FmKCMovzeBZmdhqYAAAEABAAABAoEogAmAKqyJScoERI5ALAARViwHi8bsR4ePlmwAEVYsAwvG7EMED5ZsgYeDBESObAGL7IPBgFdsAHQsAEvss8BAV1ACR8BLwE/AU8BBF2yAAEBXbECArAKK1gh2Bv0WbAGELEHArAKK1gh2Bv0WbAMELEPAbAKK1gh2Bv0WbAK0LAHELAT0LAGELAU0LACELAY0LABELAZ0LAeELEkAbAKK1gh2Bv0WbIhASQREjkwMQEhByUHByUHJQYHJQchNxc2NzcHNxc3Nwc3Mzc2NhcWFgcnNicmAwG+AYIa/pMPCAF2G/6JIzYCiST8fx0INB8TmByWBhCgG40DG/C9rr0I7QqQpCgCupICQxkCkwFEOgPDwgEWQCkDkwIRSwKSGNf5BATRswHAAwP+/wAAAQAe//AD6wSiACIAn7IdIyQREjkAsBUvsABFWLAILxuxCBA+WbIiFQgREjmwIi+yDyIBXbLPIgFdtBAiICICXbEAArAKK1gh2Bv0WbAIELEDAbAKK1gh2Bv0WbAAELAM0LAiELAN0LAiELAd0LAdL7LPHQFdth8dLx0/HQNdsgAdAV2xIAKwCitYIdgb9FmwD9CwHRCwEtCwEi+wFRCxGgGwCitYIdgb9FkwMQEhBhcWNxcGJyYmNwc3MzcjNzM2JBcWFwcmJyIGByUHIQchAxH+lQTCRYMMc2i+6QScGo0RjhqJQQEVx16FJVprZ48wAXka/okQAXgBhMsEAx3BHgIC3LUBklyTydQCAh7BHgJocwGTXAAEAAoAAAe+BKMAAwARAB8AKQCtsiAqKxESObAgELAB0LAgELAN0LAgELAT0ACwAEVYsCUvG7ElHj5ZsABFWLAoLxuxKB4+WbAARViwBC8bsQQePlmwAEVYsCAvG7EgED5ZsABFWLAjLxuxIxA+WbAEELAL0LALL7AD0LADL7YAAxADIAMDXbEAArAKK1gh2Bv0WbALELEVArAKK1gh2Bv0WbAEELEcArAKK1gh2Bv0WbIiJSAREjmyJyUgERI5MDElITchAxYWBwcGBicmJjc3NjYDBhYXFjY3NzYmJyYGBwEjAQMjEzMBEzMHCv3UGwIrm4+mCgYO0JmQpgoFDNU7B0ZHS2sOCgdGRkxsDv4f5P6JjO3K5QF3jOzIlQNCBLuRQpzCBAS+jUCdxP5dWWACBGhZTllgAgJkWvyxAyX82wSN/NoDJgAAAv/ZAAAEsgSNABYAHwCWsgAgIRESObAf0ACwAEVYsAwvG7EMHj5ZsABFWLACLxuxAhA+WbIGAgwREjmwBi+0HwYvBgJxsQUHsAorWCHYG/RZsAHQsAYQsArQsAovtB8KLwoCcbYPCh8KLwoDXbaPCp8KrwoDXbEJB7AKK1gh2Bv0WbAU0LAGELAV0LAKELAX0LAXL7AMELEfAbAKK1gh2Bv0WTAxJSEHIzcjNzM3IzczEwUWFgcGBCMnByEDFzY2NzYmJycCk/79G+0byiDJDsshyWMBzrnZCwr+8NL+DgEE1+Riiw0MV1T9mZmZtk23AjoBBcyfq9YBTQEEAQJqWU9fBAEAAAH/9P/zAoUDJAAkAHKyAiUmERI5ALAARViwDS8bsQ0aPlmwAEVYsBgvG7EYED5ZsgEYDRESOXywAS8YsA0QsQcCsAorWCHYG/RZsgkBBxESObABELEjArAKK1gh2Bv0WbITIwEREjmwGBCxHgKwCitYIdgb9FmyGx4jERI5MDETMzY2NzYnJyYHBzY2FxYWBwYGBxYHBgYnJiY1FxYXMjY3Nicj5lM9TQcJShddHLoJpn2BmQUDSVJ2BAO8i32ZsQRqNlMHDXhcAdICOC5DDQICTAFpegIDd2I7VyYpgW+CAgKDbQFZAjgvWQUAAv/xAAACegMWAAoADgBKALAARViwCS8bsQkaPlmwAEVYsAQvG7EEED5ZsgEJBBESObABL7ECArAKK1gh2Bv0WbAG0LABELAL0LIICwYREjmyDQkEERI5MDEBNwcjByM3ITcBNwEzNwcCFmQcXBy4Hv6lDQGwuv5TqjMSATkBl6OjhQHsAv4k9RgAAQAK//ICkQMVABwAbbIHHR4REjkAsABFWLACLxuxAho+WbAARViwDS8bsQ0QPlmwAhCxAwKwCitYIdgb9FmyBwINERI5sAcvsRoIsAorWCHYG/RZsgUHGhESObANELEUArAKK1gh2Bv0WbIRFBoREjmyHBoUERI5MDETEyEHJQc2NzYWBwYGJyYmJxcWFjc2Njc2JiciBzh4AeEb/rk3OENtgwQEuIJ4mwSwBDMvPEgIBzY1QTUBgwGSlgGXGQIChHR+ngICgmYBLyQBAUk5NT8BJwACABb/8gJzAyQAFAAhAF6yHSIjERI5sB0QsAfQALAARViwAC8bsQAaPlmwAEVYsA0vG7ENED5ZsAAQsQICsAorWCHYG/RZsgcNABESObAHL7EVArAKK1gh2Bv0WbANELEcArAKK1gh2Bv0WTAxAQcnJgYHNjMyFgcGBicmJjc3NjY3AyIHBwYXFjMyNjc2JgJEDgd0pTBQXWZ6BAS2g4iUCgcZ/smsTToFAwMKVjNSBgczAySbAQNba0WMc3ugAgKxjUXB4An+WD4kGxpaTjUyOwABACMAAAK7AxUABgAzALAARViwBS8bsQUaPlmwAEVYsAIvG7ECED5ZsAUQsQQCsAorWCHYG/RZsgAEBRESOTAxAQEjASE3IQKn/knNAbj+XxsCZgKf/WECf5YAAAMACP/yAoADIwAUACAALACNshctLhESObAXELAS0LAXELAk0ACwAEVYsBIvG7ESGj5ZsABFWLAILxuxCBA+WbIqCBIREjmwKi+23yrvKv8qA122DyofKi8qA122ryq/Ks8qA3GxGAKwCitYIdgb9FmyAxgqERI5sg0qGBESObAIELEeArAKK1gh2Bv0WbASELEkArAKK1gh2Bv0WTAxAQYGBxYHBgYnJiY3NjcmNzY2FxYWAzYmIyIGBwYWMzI2EzYmIyIGBwYWMzI2An0DQEZmBASvhn+WAwOaVgQEp3p2j94FMzAyTAcHNi4vTy8FKyYqQQcGLSYqQAJJOVgoPnFwfwICd2R8Tzpka34CAnT+RSgvOCsoMjQBfCcqMSonKzIAAgAz//UCggMjABQAIQBqsggiIxESObAIELAc0ACwAEVYsAgvG7EIGj5ZsABFWLAPLxuxDxA+WbICDwgREjmwAi+2DwIfAi8CA12wDxCxEgKwCitYIdgb9FmwAhCxFQKwCitYIdgb9FmwCBCxHAKwCitYIdgb9FkwMQEGIyImNzY2FxYWBwcGBCMnNzMWNicWNzc2JyYjIgYHBhYBsktMbXsEBrmAgYsJBRb+/NkVDQx3jkQ9OgwDAgtNNEwHBiwBNzmLc4GmAgSwkTTV3gGTAlSsAjZHGBlWVDoxQwAAAQCTAocDPAMxAAMAEgCwAi+xAQGwCitYIdgb9FkwMQEhNyEDHv11HgKLAoeqAAMBBgRHA1YGlQADAA4AGQBQALANL7AX0LAXL7EHCbAKK1gh2Bv0WbAC0LACL7AA0LAAL0APDwAfAC8APwBPAF8AbwAHXbACELAD0BmwAy8YsA0QsREJsAorWCHYG/RZMDEBFwUnBzQ2MzIWFRQGIiY3FjMyNjc2JiMiBgJh9f7wpppuTUxibJhlYQNAJDoGBCQeJjcGlQHBAeZPa2hETWhiR1E3JCQxNAAAAQAKAAAD+QSNAAsAVgCwAEVYsAYvG7EGHj5ZsABFWLAELxuxBBA+WbILBgQREjmwCy+ySQsBXbEAAbAKK1gh2Bv0WbAEELECAbAKK1gh2Bv0WbAGELEIAbAKK1gh2Bv0WTAxASEDIQchEyEHIQMhAzX+GjYCOyP82coDJSP9yS8B6AH4/srCBI3E/vIAAAP/h/5HBFAEUAArADkARwCgsidISRESObAnELA50LAnELBE0ACwAEVYsCgvG7EoHD5ZsABFWLAWLxuxFhI+WbAoELAr0LArL7EAA7AKK1gh2Bv0WbIHFigREjmwBy+yDhYHERI5sA4vsSwBsAorWCHYG/RZshssDhESObIgBygREjmwFhCxMwGwCitYIdgb9FmwBxCxPQGwCitYIdgb9FmwKBCxRAGwCitYIdgb9FkwMQEHFgcHBgQnIicGBwYWFxcWFgcGBgQnJiY3NjcmNzY2NyYmNzc2Njc3FxchASYnBgcGFjMyNjc2JicDBhYXMjY3NzYmJyYGBwQ2gyAJBBf+7bpDUjIHBik6rbO0BwWX/uSHz+kEB9AhBgdWO0dDBQMQ9bcoKnABdfzwOB5jDglxZ4W4DQk/V78GYFBYhQ0DBmBQVIgOA6ABXF4fo8cCFDInICIDAgaYg2aiYgMFjnilZjI9SWUmNphYIZbFCgEDE/veAwU7WT9JW0ozOAMCrUlgAmhOFU1fAgJmVAAAAgA7/+MEkQRUABIAIwBwshkkJRESObAZELAK0ACwAEVYsAovG7EKHD5ZsABFWLAOLxuxDhw+WbAARViwAi8bsQIQPlmwAEVYsBIvG7ESED5ZsgACChESObINCgIREjmwAhCxGAGwCitYIdgb9FmwChCxIAGwCitYIdgb9FkwMSUGJyYmPwI2ABcWFhc3MwMTIwEGFxYWFxY2PwInJicmBgcDEI7jq7kJAwgnAQbBbaAnRNzMEdP+MgYCAlxSZqIgBgEEG491mhvF4gcF/9wtOfoBKgUDcWbF/dP98wHyMzlldQIDvpwuRDXcBwXHwgAAAgAu//8E8AWwAB4AJwBjsiAoKRESObAgELAe0ACwAEVYsAMvG7EDID5ZsABFWLAVLxuxFRA+WbAARViwAS8bsQEQPlmyIAMBERI5sCAvsR4BsAorWCHYG/RZsgoeIBESObADELEnAbAKK1gh2Bv0WTAxAQMjEwUyFgcGBgcWFxYHBwYXFhcHByYnJjc3NicmJyUXMjY3NiYnJQGMaPb8Afbh7w8Ij5OUEQUGFAcEBCQC9SMFAwoSBgYUlP7w/4uiDg1paP7ZAlb9qgWwAdvCcKk9QKs0Nos3JD0pGwEsSixMeTAqjAnLAXdwam8EAQAAAQAuAAAFewWwAAwAWQCwAEVYsAQvG7EEID5ZsABFWLAILxuxCCA+WbAARViwAi8bsQIQPlmwAEVYsAsvG7ELED5ZsgYCBBESObAGL7IfBgFxsQEBsAorWCHYG/RZsgoBBhESOTAxASMDIxMzAzMBIQEBIQI+rmz2/PZqfQIKAT79mAGG/ugCcP2QBbD9nAJk/Tv9FQAAAQAUAAAERQYAAAwAVACwAEVYsAQvG7EEIj5ZsABFWLAILxuxCBw+WbAARViwAi8bsQIQPlmwAEVYsAsvG7ELED5ZsgcIAhESObAHL7EAAbAKK1gh2Bv0WbIKAAcREjkwMQEjAyMBMwMzASEBASEBxXJS7QEL7JddAU8BJf5JARj+/QHZ/icGAPycAZ7+Bf3BAAABAC4AAAVnBbAACwBMALAARViwAy8bsQMgPlmwAEVYsAcvG7EHID5ZsABFWLABLxuxARA+WbAARViwCi8bsQoQPlmyAAMBERI5sgUDARESObIJAAUREjkwMQEDIxMzAzcBIQEBIQGZdfb89nYCAngBQ/0vAeX+4wKj/V0FsP19AQKC/Sr9JgAAAQAUAAAENQYYAAoATACwAEVYsAMvG7EDIj5ZsABFWLAGLxuxBhw+WbAARViwAS8bsQEQPlmwAEVYsAkvG7EJED5ZsgAGARESObIFBgEREjmyCAAFERI5MDEBAyMBMwMBIQEBIQFYV+0BD+2aAYoBNf37AWL+9QH1/gsGGPyRAZH+Af3FAAACAAoAAAQaBI0ACgAWAEWyDhcYERI5sA4QsALQALAARViwAi8bsQIePlmwAEVYsAAvG7EAED5ZsQ0BsAorWCHYG/RZsAIQsRYBsAorWCHYG/RZMDEzEwUeAgcHBgAhEwMXMjY3NzYnJiYnCsoBYpfhbBAFHf6h/vcfhnCpzxgGCAYKeW4EjQEEj/2ZLP3+xgPJ/PkBwbUsR0BocgQAAAEAOf/sBEkEowAcAFCyEx0eERI5ALAARViwCy8bsQsePlmwAEVYsAMvG7EDED5ZsgALAxESObIOCwMREjmwCxCxEgGwCitYIdgb9FmwAxCxGgGwCitYIdgb9FkwMQEGBCcuAjc3EgAXFhYXJyYmJyYGBwYXFBYXFjcD/Bz+39SQyVkSBiABQenC4grrA2BrhbAaEAFkYeM4AYW93AQCkP+fNAEOAUEGBN29AWdwBAXAtIk/cH8ECNoAAAMACgAABAAEjQAOABYAHgCvshgfIBESObAYELAC0LAYELAW0ACwAEVYsAEvG7EBHj5ZsABFWLAALxuxABA+WbIYAAEREjmwGC+yvxgBcrSvGL8YAl20bxh/GAJxsv8YAXGyDxgBcrSPGJ8YAnKyXxgBcrLPGAFxsj8YAXG0HxgvGAJdsnkYAV2ySRgBXbEWAbAKK1gh2Bv0WbIIFhgREjmwABCxEQGwCitYIdgb9FmwARCxHgGwCitYIdgb9FkwMTMTBQQXFgcGBxYWBwYGIwMDFzY2NzYnJxc2Njc2JycKygGUASZUHgYKz0tUBAj33pA2z2V6DBam18FfcgwUss0EjQEIpDlTrFcaiFmksgH7/scBA1JJkgmrAQNPRYgFAQAAAv+bAAAEBQSNAAcACgBHALAARViwBC8bsQQePlmwAEVYsAIvG7ECED5ZsABFWLAGLxuxBhA+WbIJBAIREjmwCS+xAAGwCitYIdgb9FmyCgQCERI5MDElIQcjATMTIwEhAwLu/i6I+QKT2v3m/mIBSFf5+QSN+3MBsgG4AAEA6wRpAjYGLQAHABYAsABFWLAALxuxACI+WbAE0LAELzAxARcGBwcjNzYBtYFRFhbOER8GLVd9dnp31wAAAgEEBNEDegZ+AAsADwBbALADL7AG0LAGL0ALDwYfBi8GPwZPBgVdsADQsAAvsAMQsQkGsAorWCHYG/RZsAYQsA/QsA8vsAzQsAwvQA8PDB8MLww/DE8MXwxvDAddsA8QsA7QGbAOLxgwMQEGBicmJicXBhcWNyUzFyMDegi1jIugAqoEgIYb/tGiTm0FsWh4AwN4ZAJvAgJzzcAAAv0mBL3/eQaMABMAFwCOALADL7AH0LAHL0ALDwcfBy8HPwdPBwVdsAMQsAnQsAkvsAcQsQ0IsAorWCHYG/RZsAMQsREIsAorWCHYG/RZsA0QsBPQsAMQsBbQfLAWLxhAEw8WHxYvFj8WTxZfFm8WfxaPFgldsg8WAXGwFNCwFC9ADQ8UHxQvFD8UTxRfFAZdsBYQsBfQGbAXLxgwMQMGBiMiJiYHBgcnNjYzMhYWNzY3JxcHI5gKXEMlfCQVOx9lCl5EI38lFDsfXc/WmwWEVmNBCQEDVBxUZkIJAQNT7QHSAAIA3ATnBS0GkAAGAAoAWwCwAy+wBdCwBS+wANCwAC9ACQ8AHwAvAD8ABF2wAxCwAtAZsAIvGLIEAwAREjmwBtAZsAYvGLADELAJ0LAJL7AH0LAHL7YPBx8HLwcDXbAJELAK0BmwCi8YMDEBMxcjJwcjARcBIwIinfC5grLmA2no/wCqBeH6jY0BqQH+9gACABME2gOoBoMABgAKAFsAsAMvsATQGbAELxiwANAZsAAvGLADELAB0LABL7AG0LAGL0AJDwYfBi8GPwYEXbICAwYREjmwAxCwCNCwCC+wB9AZsAcvGLAIELAK0LAKL7YPCh8KLwoDXTAxASMnByMlMwUjAzMDqLuBsuUBRp3+h4qixwTajY36XAELAAIA2ATnBJQGywAGABUAaQCwAy+wBNAZsAQvGLAA0BmwAC8YsAMQsAHQsAEvsAMQsAXQsAUvQAkPBR8FLwU/BQRdsgIDBRESObADELAH0LAHL7AO0LAOL7I/DgFdsggHDhESObEPBrAKK1gh2Bv0WbIUCAcREjkwMQEjJwcnJTMXNzc2NzYnJzcWFgcGBwcDqqeRydEBObaoCyJaBwdNKg93gQEDiAkE56GhAfl0fQMKMy8GAmoDU0hrGT0AAgDXBOcDqQbQAAYAGgCQALADL7AE0BmwBC8YsADQGbAALxiwAxCwAdCwAS+wAxCwBdCwBS9ACQ8FHwUvBT8FBF2yAgMFERI5sArQsAovQAk/Ck8KXwpvCgRdsA7QsA4vQA0PDh8OLw4/Dk8OXw4GXbAKELAQ0LAQL7AOELEUBrAKK1gh2Bv0WbAKELEYBrAKK1gh2Bv0WbAUELAa0DAxASMnByMlMzcGBiMiJiYHBgcnNjYzMhYWNzY3A6mllcXTAUuP5glVOyNuJBIzIFoKUzwhcyESORwE542N7d9EWz0JAgNDGEhaPggBBEUAAAEACgAABBUFxAAHADOyAwgJERI5ALAARViwBi8bsQYePlmwAEVYsAUvG7EFED5ZsAYQsQIBsAorWCHYG/RZMDEBMwMhAyMTIQMn7ln946jtygIdBcT+Bfw3BI0AAgEEBNADegZ+AAwAEABbALADL7AG0LAGL0ALDwYfBi8GPwZPBgVdsADQsAAvsAMQsQkGsAorWCHYG/RZsAYQsA/QsA8vsA3QsA0vQA8PDR8NLw0/DU8NXw1vDQddsA8QsBDQGbAQLxgwMQEGBicmJicXBhcWNjcnFwcjA3oItYyLoAKqBIA6WQ5Aw8aPBbBoeAMDeGQCbwIBNzvOAb4AAAIBBQTSA24HCAAMABsAXwCwAy+wBtCwBi9ACw8GHwYvBj8GTwYFXbAA0LAAL7ADELEJBrAKK1gh2Bv0WbAGELAb0LAbL7AU0LAUL7Q/FE8UAl2yDhsUERI5sRUMsAorWCHYG/RZshoOGxESOTAxAQYGJyYmJxcGFxY2Nyc3NzY3NicnNxcWFQYHBwNuCbGIg6ICpgR+OlgO0AowVwkJXyoNSNgDlwkFsWt0AgJ2ZgJsAgE1Ohl2AgYwKwQBYQQTeF0YPP//AIoCiAL/Bb0DBwHHAHMCmAATALAARViwBy8bsQcgPlmwEdAwMQD//wBkApgC7QWuAwcCIABzApgAEwCwAEVYsAkvG7EJID5ZsA3QMDEA//8AfQKKAwQFrQMHAiEAcwKYABAAsABFWLABLxuxASA+WTAx//8AiQKKAuYFvAMHAiIAcwKYABMAsABFWLAULxuxFCA+WbAV0DAxAP//AJYCmAMuBa0DBwIjAHMCmAAQALAARViwBS8bsQUgPlkwMf//AHsCigLzBbsDBwIkAHMCmAAZALAARViwEi8bsRIgPlmwGNCwEhCwJNAwMQD//wCmAo0C9QW7AwcCJQBzApgAEwCwAEVYsAgvG7EIID5ZsBzQMDEAAAEAb//nBUYFyAAdAFCyDB4fERI5ALAARViwDS8bsQ0gPlmwAEVYsAMvG7EDED5ZsgANAxESObIRAw0REjmwDRCxEwGwCitYIdgb9FmwAxCxGgGwCitYIdgb9FkwMQEGACcuAicmNzYSJBcWABcjAicmAAMHBhYXFjY3BN4j/rH1kt6FCwgZI9MBKK3fAQoK9Q39yP8AEgIDk4iLuSYB3OP+7gQDhPuec5LNAUekAwT+9OcBJAcG/pf+5i+92AQGnI8AAQBx/+gFSgXIACQAX7IVJSYREjkAsABFWLAOLxuxDiA+WbAARViwAy8bsQMQPlmyEQ4DERI5sA4QsRQBsAorWCHYG/RZsAMQsR4BsAorWCHYG/RZsiMOAxESObAjL7EiAbAKK1gh2Bv0WTAxJQYEJy4CJyY3NzYSJBcWBBcnAicmBgYHBhcUFhYXFjcTITchBMBL/tq2mOyODggLBBvPATW23gEFEvAX9XTDiRcMAUiOYLpwNf7lIgIQvGNxAwOE+p5WXifTAVu1AwT03QEBAAgDf/ubXj11u2UBBVgBG8AAAAIALgAABR0FsAALABYARbIPFxgREjmwDxCwCtAAsABFWLACLxuxAiA+WbAARViwAC8bsQAQPlmxDgGwCitYIdgb9FmwAhCxFgGwCitYIdgb9FkwMTMTBTIEEgcHBgIEBxMDFzIANzYnJiYnLvwBmL0BG4MVBRnX/qbGCraa0wEpKhwPFLGRBbABt/69xizG/r24AgTk++YBAQHYkHeTowQAAAIAcv/oBXIFyAATACcASLIKKCkREjmwChCwG9AAsABFWLALLxuxCyA+WbAARViwAC8bsQAQPlmwCxCxGgGwCitYIdgb9FmwABCxJAGwCitYIdgb9FkwMQUuAicmNzc2EiQXHgIXFgICBAE2NzQmJicmAAcHBhUUFhYXFgA3An+P4YgNCAoMItUBM62Q4IgNDmTW/uYBTgYBQYNctf71IgIGQoNcsAECJxUDh/6gVldSwgFHrQMDhvyerv6Z/uqPAw40Om69ZAMF/sv2DzQ6cMBnAwcBIeUAAAIAcv8DBWwFyAAZACsASLIhLC0REjmwIRCwA9AAsABFWLAQLxuxECA+WbAARViwBS8bsQUQPlmwEBCxIAGwCitYIdgb9FmwBRCxJwGwCitYIdgb9FkwMSUXBycGIy4CJyY3NzYSJBcWFhIXFgcHBgIDNjc0JiYnJgYCFRQWFxY2EjcD2Mau9UY4kt2IDQcKCSDVATSxk+GHDAYKCB/ICAcBP4NeiduGl4pzxo4WU8aK9AsDhv+hV1c+xgFQsQMDiP8AnVhXN8r+xQI/NTpyvGUDBK7+wri83QQFfQECmgAAAQCrAAADNQSMAAYAMwCwAEVYsAUvG7EFHj5ZsABFWLAALxuxABA+WbIEAAUREjmwBC+xAwGwCitYIdgb9FkwMSEjEwU3JTMCce2X/pAmAkAkA2R618sAAAEAHwAABAoEoAAZAFeyChobERI5ALAARViwES8bsREePlmwAEVYsAAvG7EAED5ZsgMRABESObARELEJAbAKK1gh2Bv0WbINEQAREjmyFwARERI5sAAQsRkBsAorWCHYG/RZMDEhITcBNzY3NiYnJgYHBz4CFxYWBwYHBwEFA6X8eh4CGz1tDglTTmSKEOsJiOKCttAKDLdN/qcCMKkBpDNfZUZUAgJ6YgJ3vWgBBbKVp51A/vUCAAAB/3/+oAQVBI0AGABcsgUZGhESOQCwDC+wAEVYsAIvG7ECHj5ZsQABsAorWCHYG/RZsgQAAhESObIFDAIREjmwBS+wDBCxEQGwCitYIdgb9FmwBRCxFgOwCitYIdgb9FmyGBYFERI5MDEBITchBwEWFgcGBgQnJic3FhcWNjcSJSc3AsD91CMDXhv+ZJOnDQ6s/tyqstJKj6Oh6RMj/uFlEgPJxJr+hh70oaL5iwMDZrRZAgLAlwEKFAKGAAL/0f7EBCMEjAAKAA4AUwCwAEVYsAkvG7EJHj5ZsABFWLACLxuxAhA+WbAARViwBi8bsQYQPlmxAAGwCitYIdgb9FmwBhCwBdCwBS+yCAYAERI5sAAQsAzQsg0JAhESOTAxJTMHIwMjEyE3ATMBIRMHA3KxIrA37Tf9bRUDOfz81wGUdx7Cw/7FATugA+38NgKDLAAB/9T+nQROBIwAHABgsgcdHhESOQCwDy+wAEVYsAEvG7EBHj5ZsQMBsAorWCHYG/RZsgcBDxESObAHL7EaAbAKK1gh2Bv0WbIFGgcREjmwDxCxFAGwCitYIdgb9FmyEhQaERI5shwaFBESOTAxExMhByEDNhcyFhYHBgYEJyYnNxYXFjY3NiYnJgdZ4QMUJf2vcWOAeq9QDQ+e/vekz7lad7KRzBMOaGmUSAF2AxbS/qg2AnrfiZfzjQIEda9kAgK+ln+fAwRyAAEAJ/7EBFQEjAAGACYAsAEvsABFWLAFLxuxBR4+WbEDAbAKK1gh2Bv0WbIAAwUREjkwMQEBIwEhNyEEOvzm+QMM/U0jA7ED+frLBQXDAAIBBATNA4IG2wALACAAeQCwAy+wBtCwBi9ACw8GHwYvBj8GTwYFXbAA0LAAL7ADELEJBrAKK1gh2Bv0WbAAELAQ0LAQL7AT0LATL0ALDxMfEy8TPxNPEwVdsBAQsBXQsBUvsBMQsRkIsAorWCHYG/RZsBAQsR4IsAorWCHYG/RZsBkQsCDQMDEBBgYnJiYnFwYXFjcTBgcGByImBwYHJzY2MzIWFxY3NjcDcQiyi4WhAqgEfYUbvQosLkYoiSg7H2YJXkYWJy9GKDwfBbBreAICe2YCbgICcgERVDIzAk4DA1QbUGsNGicDA1MAAf+y/poA/gC1AAMAEgCwBC+wAtCwAi+wAdCwAS8wMRMjEzOg7l7u/poCGwACADr/8gahBJ8AGAAkAJayASUmERI5sAEQsBvQALAARViwDC8bsQwePlmwAEVYsA8vG7EPHj5ZsABFWLACLxuxAhA+WbAARViwAC8bsQAQPlmwDxCxEQGwCitYIdgb9FmyFAAPERI5sBQvsRUBsAorWCHYG/RZsAAQsRgBsAorWCHYG/RZsAIQsRkBsAorWCHYG/RZsAwQsRwBsAorWCHYG/RZMDEhIQUjJiYCNzc2EjYXMhYzIQchAyEHIQMhBTcTJyYGBwYXFhYXBdn9e/7yTpHQXREGF6L/nVnEXQKBI/3KMAHoI/4aNgI7/GtllsSCtiEWBQVqXQ4ClAEDnTapAQiQARHE/vLD/soMBAMWDAK0qXBjcIQEAAACAEf+sARGBKMAGQAoAFSyIykqERI5sCMQsATQALAVL7AARViwDC8bsQwePlmwFRCxAAGwCitYIdgb9FmyBRUMERI5sAUvsRoBsAorWCHYG/RZsAwQsSIBsAorWCHYG/RZMDEFFjY3BicmAjc+AhcWFhIHBwYCBCcmJzcWARY3NzYnJiYnJgYGFxYWAVCR2lCCmbzNFA6U6IuTy1gTHSTF/uStjJFBcgEioXEcBwIDZFpbjkcKCV6LA7nSXQQCARXXk/iGAgSR/v6iwvH+pqsDAj20LwHpBHuuODxoegMDeNZnXG0AAAH/B/5GAT8AzQAMAC4AsA0vsABFWLAELxuxBBI+WbEJAbAKK1gh2Bv0WbANELEMBbAKK1gh2Bv0WTAxJQMGBicmJzcWMzI3EwE/KhjQokRAIjkmfiArzf70tMcCAhLFD68BDP///6j+oAQ+BI0ABgJMKQD////Y/p0EUgSMAAYCTgQA////yP7EBBoEjAEGAk33AAATALAARViwCS8bsQkePlmwDdAwMQD//wAXAAAEAgSgAAYCS/gA//8ARv7EBHMEjAEGAk8fAAAQALAARViwBS8bsQUePlkwMf//ACL/5gReBKUABgJl1AD//wBd/+gEDQW4AgYAGvoA//8ALv6wBC0EowAGAlPnAP//ADv/6ARFBcgCBgAcAAD//wD1AAADfwSMAAYCSkoA////D/5IAdwEOgIGAJwAAP///w/+SAHcBDoCBgCcAAD//wAiAAABywQ6AAYAjQAA////ff5bAcsEOgAmAI0AAAAGAKTVCv//ACIAAAHLBDoABgCNAAAAAQAK/+YD6AShACAAbLIHISIREjkAsABFWLAULxuxFB4+WbAARViwHi8bsR4QPlmwAEVYsA8vG7EPED5ZsB4QsQIBsAorWCHYG/RZsgkeFBESObAJL7EHB7AKK1gh2Bv0WbAUELEMB7AKK1gh2Bv0WbIYCQcREjkwMSUWMzI2NzYnJzc3JicmBwMjEzY2FxYWFwEWFgcGBicmJwGQRUVPbwsT0mAf7jVPsSp/6X4e8sFyv17+2IKOBgrwrm532zNuU5QCAa76NgID9/0UAuzW3wQEZ2r+0xahd6/YAgI2AAACAE7/5gSKBKUADAAdAEiyEh4fERI5sBIQsADQALAARViwBi8bsQYePlmwAEVYsAAvG7EAED5ZsAYQsREBsAorWCHYG/RZsAAQsRoBsAorWCHYG/RZMDEFJgITEgAXFhIDBwIAEzc0JicmBgcHBhcWFhcWNjcCGOLoGyQBR+/g5xsLMP7EjQVraIq8GQQGAwVsYYq7GRUFAUoBAQEhAUkFBf66/v5H/v7+3AKAU4yVBAXUwiA8QnSLBAXWxwAAAQBVAAAEYgWwAAYAMwCwAEVYsAUvG7EFID5ZsABFWLABLxuxARA+WbAFELEDAbAKK1gh2Bv0WbIAAwUREjkwMQEBIwEhNyEESP0H+gL3/V4iA5YFHPrkBO3DAAACABD/6AQjBgAAEgAfAGayBCAhERI5sAQQsBzQALAJL7AARViwDS8bsQ0cPlmwAEVYsAcvG7EHED5ZsABFWLAELxuxBBA+WbIGDQcREjmyCw0HERI5sA0QsRYBsAorWCHYG/RZsAQQsRsBsAorWCHYG/RZMDEBBgIGJyYnByMBMwM2FxYWFxYHJycmJyYHAxYXFjY3NgQaE5LWf7ddLc8BCu5seaahuwkDBuoEHJ6NZVEzi3ypGAgCGKD+84MDBIx7BgD90YEEBN+/QT5zJ7wFBIn+NYMEA8KoVAAAAQA3/+gEAwRUABsATbIAHB0REjkAsABFWLAPLxuxDxw+WbAARViwCC8bsQgQPlmxAAGwCitYIdgb9FmyBA8IERI5shMIDxESObAPELEWAbAKK1gh2Bv0WTAxJRY2NzcOAicmAjc3EgAXFhYVIyYmJyYGBwYWAfFXgxbfDobUcNPeGAIdATbfsNDdAl5SiqwIBmKtAmdTAWyvYwMFATDoFAEBATcGBOKzYnEEBvLigo0AAAIAO//nBJsGAAASAB8AY7IEICEREjmwBBCwGdAAsAcvsABFWLAELxuxBBw+WbAARViwCS8bsQkQPlmwAEVYsA0vG7ENED5ZsgYECRESObILBAkREjmxGAGwCitYIdgb9FmwBBCxHQGwCitYIdgb9FkwMRM2EjYXFhcTMwEjNwYnJiYnJjczBhcWFhcWNxMmJyYGRBOW1oGjX2jt/vbMDH+um74MBAbuBgQFYleFZ1Q1g32sAh+jAQyEAwR2Aiv6AHWOBATluz88NTtnfgQEhQHaeAQDwgACACr+UARMBFEAHAAqAH+yBCssERI5sAQQsCfQALAARViwBy8bsQccPlmwAEVYsAQvG7EEHD5ZsABFWLAMLxuxDBI+WbAARViwFi8bsRYQPlmyBgcWERI5sAwQsREBsAorWCHYG/RZshQHFhESObAWELEiAbAKK1gh2Bv0WbAEELEnAbAKK1gh2Bv0WTAxEzYSNhcWFzczAwYAJyYnNxYXBBM3BicuAicmNxcGFxYWFxY3EyYnJgYHRBOU14G2WirPqiL+1+Sum0JzjAEFSgd+oGWdXAYEBu4GBAViVYpkVTSGfqwXAh+jAQyDAwSDc/wZ8f7uBARZsk0CBwE8G3wEAWjDdj89ATU7Z30DBYUB23cEA8amAAIANP/oBD8EUQATACMARbIYJCUREjmwGBCwBNAAsABFWLAFLxuxBRw+WbAARViwDi8bsQ4QPlmxFwGwCitYIdgb9FmwBRCxHwGwCitYIdgb9FkwMRM2Ejc2Fx4CBwcGAgYnJiYnJjcXFhYXFjY3NicmJicmBgcGRRa7kmV5jMxhEAIUoPuTjc4vLQ/rB2lae7McBgQJall+shcIAiCwARNBLQMCkPyWFp7+/40EApJ/e5F2aXwDBcS9OD5rfwMDy6VRAAAC/8f+YAQhBFIAEgAeAGKyBB8gERI5sAQQsB3QALAARViwDS8bsQ0cPlmwAEVYsAovG7EKHD5ZsABFWLAHLxuxBxI+WbAARViwBC8bsQQQPlmwDRCxFwGwCitYIdgb9FmwBBCxHAGwCitYIdgb9FkwMQEGAgYnJicDIwE3BzYXFhYXFgcnNzQmJyYHAxYXFjYEGBOR1n+oYWHuAQTSDnyvnr0JAwbtBGZfhGNXMod6sQIYnv7zhQMEc/3+BdoBcokEAuS9QD4BS36NBAR8/hV0BAPGAAIAO/5gBEsEUQASAB4AbbIMHyAREjmwDBCwGNAAsABFWLAHLxuxBxw+WbAARViwBC8bsQQcPlmwAEVYsAkvG7EJEj5ZsABFWLANLxuxDRA+WbIGBw0REjmyCwcNERI5sRcBsAorWCHYG/RZsAQQsRwBsAorWCHYG/RZMDETNhI2FxYXNzMBIxMGJyYmJyY3FwcUFhcWNxMmJyYGRBKS2YWvXCrQ/vztY3mdnMAMBAbuBGReg2RZN399sQIfngEOhgMEf2/6JgH9dQQC4b8/PQFKe5QCBHkB928DA8cAAAIAO//rBAgEVAAVAB4Ag7IAHyAREjmwFtAAsABFWLAILxuxCBw+WbAARViwAC8bsQAQPlmyGQgAERI5sBkvtL8ZzxkCXbRfGW8ZAnG0HxkvGQJxso8ZAV207xn/GQJxsQwHsAorWCHYG/RZsAAQsRABsAorWCHYG/RZshIACBESObAIELEWAbAKK1gh2Bv0WTAxBS4CNzc2ABcWEgcHIQYWFxY3FwYGAyYDBTc2JyYmAg2Q2GoOAhkBOdfHzRsT/VgKhn2Jki0+vRHAYgHCBggFCFgTAYj0lxT+AUEGBP7q4oJ/nwIEUagzNwOhBv7wAR0vK0JPAAIAMP5QBDoEUQAbACkAf7IEKisREjmwBBCwJtAAsABFWLAHLxuxBxw+WbAARViwBC8bsQQcPlmwAEVYsAwvG7EMEj5ZsABFWLAWLxuxFhA+WbIGBxYREjmwDBCxEQGwCitYIdgb9FmyFAcWERI5sBYQsSEBsAorWCHYG/RZsAQQsSYBsAorWCHYG/RZMDETNhI2FxYXNzMDBgAnJic3FhcWEzcGJyYmJyY3MwYXFBYXFjcTJicmBgdGFIbOgrVcK86tIv7Y4aCSQmx7+EwRfp+asAcDBu0GAVhWi2JSMIh5nxYCH6UBBocCBIRz/Azt/vcEBEyxPwIHARBFegQE4ME+OzM7aH8EBIkB1HoEA8GrAP//AKQAAAMtBbUABgAVtQAAAwAi/+wERwSgABwAJQAxAG2yLjIzERI5sC4QsBLQsC4QsB/QALAARViwDC8bsQwePlmwAEVYsBkvG7EZED5ZsABFWLAALxuxABA+WbITDBkREjmxHQGwCitYIdgb9FmyIAwZERI5sikMGRESObAMELEvB7AKK1gh2Bv0WTAxBSYmNzY3NycmNzY2FxYWBwYHBxc2NzMGBxcjJwYnFjcnBwYHBhYTBhYXNzY3NiYjIgYBlaPQBwipdS5BBwfMkYesBwe8T69PFs4VpJfxN7GiY3O8QloLCkldBBcwLWkJBTgrL08QA6CCnHRISGxhf58CApt2mncy52WF3aLMSl7GAjP/Kj1JOkMCuR48Ox1ERy0yQgAAAf/tAAADPQSNAA0APrILDg8REjkAsABFWLAKLxuxCh4+WbAARViwBC8bsQQQPlmxAgGwCitYIdgb9FmyBgoEERI5sgwKBBESOTAxAQcDIQchEwc3NxMzAyUCZv9FAhsi/PhagBuAVu1JAQACmUz+dcICAyWbJgHu/lxLAAL/ggAABgwEjQAPABIAirISExQREjmwEhCwCtAAsABFWLAKLxuxCh4+WbAARViwBC8bsQQQPlmwAEVYsAgvG7EIED5Zsg8KBBESOXywDy8YsQABsAorWCHYG/RZsAQQsQIBsAorWCHYG/RZshEKBBESObARL7EGAbAKK1gh2Bv0WbAKELEMAbAKK1gh2Bv0WbISCgQREjkwMQEhAyEHIRMhAyEBIQchAyEFIRMFXv5TLAILIv0TJP6Vrf71AyUDZSP+BiQBtvw3AQZFAf7+wsABB/75BI3B/vT5AfcAAAIACgAAA8AEjQAMABUAWbIDFhcREjmwAxCwFdAAsABFWLAALxuxAB4+WbAARViwCy8bsQsQPlmwABCwFbAKK1jYG9xZsQMBsAorWCHYG/RZsg8ACxESObAPL7EJAbAKK1gh2Bv0WTAxEzMHFxYWBwYEIycHIwEDFzI2NzYmJ9TuIXPI5AsK/vHboijtAXZAh2iODQtZWwSNtwECy6Su1AHjAxL+lAFiW0xeBQAD/9z/yQS6BLoAFQAdACYAarIbJygREjmwGxCwEdCwGxCwJNAAsABFWLARLxuxER4+WbAARViwBi8bsQYQPlmyGAYRERI5shkRBhESObARELEbAbAKK1gh2Bv0WbIgEQYREjmyIQYRERI5sAYQsSMBsAorWCHYG/RZMDEBFgcHAgAnJicHJzcmNzcSABcWFzcXAQYXASYnJgIFNicBFhcWNjcEDVoaBB/+u/CUc2GxsGEbBiQBSueacl2w/GsECAH9NlmaxgI5AgX+BjdWmb4WA/iw5iP+8v66BAJHbwHFqvAuAQ4BPQQCTGoB/QY3NwJXLwQG/vwTMzX9rCwCBPr/AAACAB4AAAT1BI0AEwAXAJGyAxgZERI5sAMQsBfQALAARViwDC8bsQwePlmwAEVYsBAvG7EQHj5ZsABFWLACLxuxAhA+WbAARViwBi8bsQYQPlmwEBCwALAKK1jYG9xZshUMAhESObAVL7EEAbAKK1gh2Bv0WbAAELAI0LAJ0LAAELETDrAKK1gh2Bv0WbAK0LATELAO0LAJELAW0LAX0DAxASMDIxMhAyMTIzczNzMHITczBzMBITchBORgkexT/gRT7ZNXHVcb7RsB/BvsGE/8igH8H/4FA0/8sQHb/iUDT6qUlJSU/qWxAAABAAr+RgSoBI0AEwBbsgMUFRESOQCwAEVYsA8vG7EPHj5ZsABFWLASLxuxEh4+WbAARViwAy8bsQMSPlmwAEVYsA0vG7ENED5ZsAMQsQgBsAorWCHYG/RZsgwPAxESObIRDwMREjkwMQUGBiciJzcWMzI3NwEDIxMzARMzA9gY0aE8SSM2KYMgCP6NjO3K5QF3jOw+tMgCFMUOvzEDIvzbBI382gMmAP//ADYCCQJYAs0CBgARAAAAAv//AAAE/gWwAA8AHgBssh4fIBESObAeELAO0ACwAEVYsAUvG7EFID5ZsABFWLAALxuxABA+WbIDAAUREjl8sAMvGLECB7AKK1gh2Bv0WbAR0LAAELETAbAKK1gh2Bv0WbAFELEcAbAKK1gh2Bv0WbADELAd0LAe0DAxMxMjNzMTBTIEEgcHBgIEIxMjAxcyJDc2JyYmJycDM0Vxtx62bgGKtgEHdhcLHs3+vMKf3U6SxgEFJRoHCZeGuUveAoyqAnoBtf7BwE/J/smsAoz+PgH73ZhxkaQEAf5SAAL//wAABP4FsAAPAB4AbLIeHyAREjmwHhCwDtAAsABFWLAFLxuxBSA+WbAARViwAC8bsQAQPlmyAwAFERI5fLADLxixAgewCitYIdgb9FmwEdCwABCxEwGwCitYIdgb9FmwBRCxHAGwCitYIdgb9FmwAxCwHdCwHtAwMTMTIzczEwUyBBIHBwYCBCMTIwMXMiQ3NicmJicnAzNFcbcetm4BirYBB3YXCx7N/rzCn91OksYBBSUaBwmXhrlL3gKMqgJ6AbX+wcBPyf7JrAKM/j4B+92YcZGkBAH+UgABACsAAAQXBgAAGgB2sgMbHBESOQCwGC+wAEVYsAQvG7EEHD5ZsABFWLARLxuxERA+WbAARViwCS8bsQkQPlmyLxgBXbIPGAFdshoRGBESObAaL7EBB7AKK1gh2Bv0WbICEQQREjmwBBCxDgGwCitYIdgb9FmwARCwE9CwGhCwFtAwMQEjAzYXFhYHAyMTNicmJyYHAyMTIzczNzMHMwLL5DKHrJqVE3TtdgUDDYOEaIft1L8evhnuHOIEx/78jgQC1r39SAK7KyV6AwKE/PoEx6qPjwAAAQCcAAAFIgWwAA8ATgCwAEVYsAovG7EKID5ZsABFWLACLxuxAhA+WbIPCgIREjmwDy+xAAewCitYIdgb9FmwBNCwDxCwBtCwChCxCAGwCitYIdgb9FmwDNAwMQEjAyMTIzczEyE3IQchAzMDvsmI9onNHsw0/kskBGIk/kg0ygMS/O4DEqoBKMzM/tgAAAH/4v/tAq4FQQAeAIOyFx8gERI5ALAARViwFS8bsRUcPlmwAEVYsBkvG7EZHD5ZsABFWLALLxuxCxA+WbIeGQsREjmwHi+xAAewCitYIdgb9FmwCxCxBgGwCitYIdgb9FmwABCwD9CwHhCwEdCwFRCxEwGwCitYIdgb9FmwFRCwF9CwFy+wExCwG9CwHNAwMQEjAwYXFhcyNwcGIyYmNxMjNzM3IzczEzMDMwcjBzMCbdEtAwIGSiUvEEpLfHsNLs8ezRutIKwu7i65H7oc0gI3/vEZFEEDCb4VAqWIARuqpbQBB/75tKUA////pAAABK4HNgImACUAAAEHAEQBWwE2ABMAsABFWLAELxuxBCA+WbAM3DAxAP///6QAAATIBzYCJgAlAAABBwB1AfYBNgATALAARViwBS8bsQUgPlmwDdwwMQD///+kAAAErgc3AiYAJQAAAQcAngDyATYAEwCwAEVYsAQvG7EEID5ZsA/cMDEA////pAAABMkHKwImACUAAAEHAKUBAAE3AAkAsAQvsBXcMDEA////pAAABK4HAwImACUAAAAHAGoBKAE2////pAAABK4HlQImACUAAAEHAKMBjAFqAAwAsAQvsBTcsBfQMDH///+kAAAE2gexAiYAJQAAAAcCJwGEARz//wBl/jgFDQXHAiYAJwAAAAcAeQG6//v//wAnAAAEugc9AiYAKQAAAQcARAEjAT0AEwCwAEVYsAYvG7EGID5ZsA3cMDEA//8AJwAABLoHPQImACkAAAEHAHUBvgE9ABMAsABFWLAGLxuxBiA+WbAO3DAxAP//ACcAAAS6Bz4CJgApAAABBwCeALoBPQATALAARViwBi8bsQYgPlmwEdwwMQD//wAnAAAEugcKAiYAKQAAAAcAagDwAT3//wA1AAACMgc9AiYALQAAAQcARP/cAT0AEwCwAEVYsAIvG7ECID5ZsAXcMDEA//8ANQAAA0gHPQImAC0AAAEHAHUAdgE9ABMAsABFWLADLxuxAyA+WbAG3DAxAP//ADUAAAMSBz4CJgAtAAABBwCe/3MBPQATALAARViwAi8bsQIgPlmwCNwwMQD//wA1AAADLAcKAiYALQAAAAcAav+pAT3//wAnAAAFhgcrAiYAMgAAAQcApQEoATcACQCwBS+wFNwwMQD//wBr/+cFIQc2AiYAMwAAAQcARAFyATYAEwCwAEVYsAovG7EKID5ZsCTcMDEA//8Aa//nBSEHNgImADMAAAEHAHUCDQE2AAkAsAovsCXcMDEA//8Aa//nBSEHNwImADMAAAEHAJ4BCQE2AAkAsAovsCTcMDEA//8Aa//nBSEHKwImADMAAAEHAKUBFwE3AAkAsAovsC3cMDEA//8Aa//nBSEHAwImADMAAAAHAGoBPwE2//8AW//mBS8HNgImADkAAAEHAEQBSgE2ABMAsABFWLAKLxuxCiA+WbAU3DAxAP//AFv/5gUvBzYCJgA5AAABBwB1AeUBNgATALAARViwEi8bsRIgPlmwFdwwMQD//wBb/+YFLwc3AiYAOQAAAQcAngDhATYAEwCwAEVYsAovG7EKID5ZsBfcMDEA//8AW//mBS8HAwImADkAAAAHAGoBFwE2//8AoQAABU0HNgImAD0AAAEHAHUBvQE2ABMAsABFWLABLxuxASA+WbAL3DAxAP//ACL/6APcBgACJgBFAAABBwBEALMAAAATALAARViwGC8bsRgcPlmwLdwwMQD//wAi/+gEIAYAAiYARQAAAQcAdQFOAAAAEwCwAEVYsBgvG7EYHD5ZsC7cMDEA//8AIv/oA+kGAQImAEUAAAEGAJ5KAAATALAARViwGC8bsRgcPlmwMNwwMQD//wAi/+gEIQX1AiYARQAAAQYApVgBABMAsABFWLAYLxuxGBw+WbAv3DAxAP//ACL/6AQDBc0CJgBFAAAABwBqAIAAAP//ACL/6APcBl8CJgBFAAABBwCjAOQANAAWALAARViwGC8bsRgcPlmwNdywO9AwMf//ACL/6AQyBnwCJgBFAAAABwInANz/5///ADj+OAPuBFICJgBHAAAABwB5ATz/+///ADv/6gQCBgACJgBJAAABBwBEAJwAAAATALAARViwCS8bsQkcPlmwIdwwMQD//wA7/+oECQYAAiYASQAAAQcAdQE3AAAAEwCwAEVYsAkvG7EJHD5ZsCLcMDEA//8AO//qBAIGAQImAEkAAAEGAJ4zAAATALAARViwCS8bsQkcPlmwJNwwMQD//wA7/+oEAgXNAiYASQAAAAYAamkA//8AIgAAAecF+QImAI0AAAEGAESR+QATALAARViwAi8bsQIcPlmwBdwwMQD//wAiAAAC/QX5AiYAjQAAAQYAdSv5ABMAsABFWLADLxuxAxw+WbAG3DAxAP//ACIAAALHBfoCJgCNAAABBwCe/yj/+QATALAARViwAi8bsQIcPlmwCNwwMQD//wAiAAAC4QXGAiYAjQAAAAcAav9e//n//wANAAAEJwX1AiYAUgAAAQYApV4BABMAsABFWLADLxuxAxw+WbAW3DAxAP//ADn/6AQnBgACJgBTAAABBwBEALAAAAATALAARViwBC8bsQQcPlmwItwwMQD//wA5/+gEJwYAAiYAUwAAAQcAdQFLAAAAEwCwAEVYsAQvG7EEHD5ZsCPcMDEA//8AOf/oBCcGAQImAFMAAAEGAJ5HAAATALAARViwBC8bsQQcPlmwJdwwMQD//wA5/+gEJwX1AiYAUwAAAQYApVUBABMAsABFWLAELxuxBBw+WbAk3DAxAP//ADn/6AQnBc0CJgBTAAAABgBqfQD//wBK/+gEMQYAAiYAWQAAAQcARAC1AAAAEwCwAEVYsAgvG7EIHD5ZsBXcMDEA//8ASv/oBDEGAAImAFkAAAEHAHUBUAAAAAkAsAcvsBbcMDEA//8ASv/oBDEGAQImAFkAAAEGAJ5MAAAJALAHL7AV3DAxAP//AEr/6AQxBc0CJgBZAAAABwBqAIIAAP///7X+RQQSBgACJgBdAAABBwB1ARoAAAAJALABL7AS3DAxAP///7X+RQQSBc0CJgBdAAAABgBqTAD///+kAAAEsQbkAiYAJQAAAQcAcAD6AToAEwCwAEVYsAQvG7EEID5ZsAzcMDEA//8AIv/oBAkFrgImAEUAAAEGAHBSBAAJALAYL7As3DAxAP///6QAAASuBx0CJgAlAAABBwChATABNgAJALAEL7AO3DAxAP//ACL/6AP0BecCJgBFAAABBwChAIgAAAAJALAYL7Av3DAxAAAC/6T+UQSuBbAAFwAaAHmyFRscERI5sBUQsBrQALAARViwFS8bsRUgPlmwAEVYsAsvG7ELEj5ZsABFWLATLxuxExA+WbAARViwFy8bsRcQPlmwCxCxBgOwCitYIdgb9FmwFxCwENCwEC+yGRMVERI5sBkvsREBsAorWCHYG/RZshoVExESOTAxIRcHBgcGFxY3FwYnIiY3NjcDIQMhATMBASEDBHEFL4MHBTgbPQxFVVdpAgO0Nv3frv72AxLeARr9FgGYYwMfVlY5AwEXkCsCbVSVaQFB/q0FsPpQAh8CWgAAAgAi/lED3ARQADAAOwCesho8PRESObAaELA20ACwAEVYsCgvG7EoHD5ZsABFWLALLxuxCxI+WbAARViwAC8bsQAQPlmwAEVYsBQvG7EUED5ZsAAQsBDQsBAvshIoABESObIaFCgREjmwGi+wKBCxIAewCitYIdgb9FmyJBogERI5QAkMJBwkLCQ8JARdsBQQsTEBsAorWCHYG/RZsBoQsTYHsAorWCHYG/RZMDEhFwcGBwYXFjcXBiciJjc2Nyc1BicmJjc2JDMXNzYnJicmBgcHPgIXFhYHAwcGFwclFjY3NyciBgcGFgNKBS+DBwU4Gz0MRVVXaQIDtQSGm425BggBGOyaDgYGFHtMcw3tB4DUdrHGEVMIAxIB/iFLgC0lcYagCwhLAx9WVjkDAReQKwJtVJZpKSl9BAKxiKvEAkonImwDAlFEAmSXVAIEzaP+BVo7OBKuAkk6zQFlWENN//8AZf/oBQ0HSwImACcAAAEHAHUB+AFLAAkAsAwvsCHcMDEA//8AOP/pA/MGAAImAEcAAAEHAHUBIQAAAAkAsBEvsB/cMDEA//8AZf/oBQ0HTAImACcAAAEHAJ4A9AFLAAkAsAwvsCDcMDEA//8AOP/pA+4GAQImAEcAAAEGAJ4dAAAJALARL7Ae3DAxAP//AGX/6AUNBywCJgAnAAABBwCiAdUBVAAJALAML7An3DAxAP//ADj/6QPuBeECJgBHAAABBwCiAP4ACQAJALARL7Al3DAxAP//AGX/6AUNB1ACJgAnAAABBwCfAQsBSwAJALAML7Aj3DAxAP//ADj/6QPwBgUCJgBHAAABBgCfNAAACQCwES+wIdwwMQD//wAnAAAE4AdCAiYAKAAAAQcAnwCbAT0AEwCwAEVYsAEvG7EBID5ZsBzcMDEA//8AO//nBdUGAgAmAEgAAAAHAboEvwT8//8AJwAABLoG6wImACkAAAEHAHAAwgFBABMAsABFWLAGLxuxBiA+WbAN3DAxAP//ADv/6gQCBa4CJgBJAAABBgBwOwQACQCwCS+wINwwMQD//wAnAAAEugckAiYAKQAAAQcAoQD4AT0ACQCwBi+wD9wwMQD//wA7/+oEAgXnAiYASQAAAQYAoXEAAAkAsAkvsCPcMDEA//8AJwAABLoHHgImACkAAAEHAKIBmwFGAAkAsAYvsBTcMDEA//8AO//qBAIF4QImAEkAAAEHAKIBFAAJAAkAsAkvsCjcMDEAAAEAJ/5RBLoFsAAcAISyER0eERI5ALAARViwFy8bsRcgPlmwAEVYsBAvG7EQEj5ZsABFWLAELxuxBBA+WbAARViwFS8bsRUQPlmyGxUXERI5sBsvsQEBsAorWCHYG/RZsBUQsQIBsAorWCHYG/RZsAPQsBAQsQsDsAorWCHYG/RZsBcQsRkBsAorWCHYG/RZMDEBIQMhByMXBwYHBhcWNxcGJyImNzY3IRMhByEDIQPT/bxOAqYjcQUvgwcFOBs9DEVVV2kCA5b9sPwDlyT9YUYCRQKK/kDKAx9WVjkDAReQKwJtVIxgBbDM/m4AAgA8/mwECARRACMALACksgYtLhESObAGELAk0ACwAEVYsBkvG7EZHD5ZsABFWLAMLxuxDBI+WbAARViwES8bsREQPlmwA9CyJi0ZERI5sCYvso8mAV20HyYvJgJxtJ8mryYCcbRfJm8mAnG0vybPJgJdtO8m/yYCcbQvJj8mAnKxHQewCitYIdgb9FmwERCxIQGwCitYIdgb9FmyIxEZERI5sBkQsSQBsAorWCHYG/RZMDElBgcHBgcGFxY3FwYnIiY3NjcmAjc3NhI2FxYSBwchBhYXFjcDJgMFNzYnJiYDplWNMW0IBTgbPQxFVVdpAgJgt8wRAxKd6onLyxkO/VcJemuZgcm8XgHBBAcGC1q2eDIhTFI5AwEXkCsCbVRtVRkBHM4ppQEHiAME/trsaIGeAgWKAlgG/vABFS4sR1IA//8AJwAABLoHQgImACkAAAEHAJ8A0QE9ABMAsABFWLAGLxuxBiA+WbAR3DAxAP//ADv/6gQGBgUCJgBJAAABBgCfSgAACQCwCS+wJNwwMQD//wBr/+oFFgdMAiYAKwAAAQcAngDxAUsACQCwDS+wI9wwMQD////3/k8EQgYBAiYASwAAAQYAnj4AAAkAsAQvsCzcMDEA//8Aa//qBRYHMgImACsAAAEHAKEBLwFLAAkAsA0vsCXcMDEA////9/5PBEIF5wImAEsAAAEGAKF8AAAJALAEL7Au3DAxAP//AGv/6gUWBywCJgArAAABBwCiAdIBVAAJALANL7Aq3DAxAP////f+TwRCBeECJgBLAAABBwCiAR8ACQAJALAEL7Az3DAxAP//AGv98wUWBcgCJgArAAAABwG6AZT+jP////f+TwRCBqsCJgBLAAABBwI0ATQAfgAJALAEL7Av3DAxAP//ACcAAAWHBz4CJgAsAAABBwCeARIBPQATALAARViwBy8bsQcgPlmwENwwMQD//wANAAAD+QdeAiYATAAAAQcAngBSAV0ACQCwES+wFNwwMQD//wA1AAADSgcyAiYALQAAAQcApf+BAT4ACQCwAi+wDtwwMQD//wAUAAAC/wXuAiYAjQAAAQcApf82//oACQCwAi+wDtwwMQD//wA1AAADMgbrAiYALQAAAQcAcP97AUEAEwCwAEVYsAIvG7ECID5ZsAXcMDEA//8AIgAAAucFpwImAI0AAAEHAHD/MP/9ABMAsABFWLACLxuxAhw+WbAF3DAxAP//ADUAAAMdByQCJgAtAAABBwCh/7EBPQAJALACL7AH3DAxAP//ACIAAALSBeACJgCNAAABBwCh/2b/+QAJALACL7AH3DAxAP///47+VwIoBbACJgAtAAAABgCk5gb///92/lECCQXYAiYATQAAAAYApM4A//8ANQAAAlQHHgImAC0AAAEHAKIAUwFGAAkAsAIvsAzcMDEA//8ANf/nBpwFsAAmAC0AAAAHAC4COwAA//8AH/5GBAMF2AAmAE0AAAAHAE4CBQAA//8AA//nBTEHNwImAC4AAAEHAJ4BkgE2AAkAsAAvsBDcMDEA////D/5IAscF3wImAJwAAAEHAJ7/KP/eABMAsABFWLAMLxuxDBw+WbAR3DAxAP//ACf+RAVxBbACJgAvAAAABwG6AV/+3f//ABH+LwRKBgACJgBPAAAABwG6AO7+yP//ACcAAAPDBzYCJgAwAAABBwB1AGoBNgATALAARViwBS8bsQUgPlmwCNwwMQD//wAfAAADPQeRAiYAUAAAAQcAdQBrAZEAEwCwAEVYsAMvG7EDIj5ZsAbcMDEA//8AJ/4DA8MFsAImADAAAAAHAboBJf6c////ov4DAhcGAAImAFAAAAAHAbr/0/6c//8AJwAAA98FsQImADAAAAEHAboCyQSrABAAsABFWLAKLxuxCiA+WTAx//8AHwAAA3QGAgAmAFAAAAEHAboCXgT8AAYAsAgvMDH//wAnAAADwwWwAiYAMAAAAAcAogFc/dT//wAfAAAC8wYAACYAUAAAAAcAogDy/a///wAnAAAFhgc2AiYAMgAAAQcAdQIeATYAEwCwAEVYsAgvG7EIID5ZsAzcMDEA//8ADQAABCYGAAImAFIAAAEHAHUBVAAAAAkAsAMvsBXcMDEA//8AJ/3/BYYFsAImADIAAAAHAboBkP6Y//8ADf4DA/oEUgImAFIAAAAHAboA+v6c//8AJwAABYYHOwImADIAAAEHAJ8BMQE2ABMAsABFWLAGLxuxBiA+WbAP3DAxAP//AA0AAAQjBgUCJgBSAAABBgCfZwAACQCwAy+wF9wwMQD//wANAAAD+gYDAiYAUgAAAQcBugBABP0ABgCwFy8wMf//AGv/5wUhBuQCJgAzAAABBwBwAREBOgAJALAKL7Aj3DAxAP//ADn/6AQnBa4CJgBTAAABBgBwTwQACQCwBC+wIdwwMQD//wBr/+cFIQcdAiYAMwAAAQcAoQFHATYACQCwCi+wJtwwMQD//wA5/+gEJwXnAiYAUwAAAQcAoQCFAAAACQCwBC+wJNwwMQD//wBr/+cFdwc1AiYAMwAAAQcApgGOATYADACwCi+wJdywJ9AwMf//ADn/6AS1Bf8CJgBTAAABBwCmAMwAAAAMALAEL7Aj3LAl0DAx//8AJwAABNgHNgImADYAAAEHAHUBqAE2AAkAsAQvsBrcMDEA//8AEAAAA4YGAAImAFYAAAEHAHUAtAAAAAkAsAsvsBDcMDEA//8AJ/4DBNgFsAImADYAAAAHAboBJv6c////nP4DAu8EUwImAFYAAAAHAbr/zf6c//8AJwAABNgHOwImADYAAAEHAJ8AuwE2AAkAsAQvsBzcMDEA//8AEAAAA4QGBQImAFYAAAEGAJ/IAAAJALALL7AS3DAxAP//ACT/6gS7BzYCJgA3AAABBwB1AcQBNgAJALAKL7As3DAxAP//ABz/6QQDBgACJgBXAAABBwB1ATEAAAAJALAIL7An3DAxAP//ACT/6gS7BzcCJgA3AAABBwCeAMABNgAJALAKL7Ar3DAxAP//ABz/6QPMBgECJgBXAAABBgCeLQAACQCwCC+wJtwwMQD//wAk/j0EuwXHAiYANwAAAAcAeQGQAAD//wAc/jQDxARQAiYAVwAAAAcAeQFC//f//wAk/fkEuwXHAiYANwAAAAcBugE+/pL//wAc/e8DxARQAiYAVwAAAAcBugDw/oj//wAk/+oEuwc7AiYANwAAAQcAnwDXATYACQCwCi+wLtwwMQD//wAc/+kEAAYFAiYAVwAAAQYAn0QAAAkAsAgvsCncMDEA//8AnP38BSIFsAImADgAAAAHAboBLf6V//8AO/35Aq4FQQImAFgAAAAHAboAg/6S//8AnP5ABSIFsAImADgAAAAHAHkBfwAD//8AO/49Aq4FQQImAFgAAAAHAHkA1QAA//8AnAAABSIHOwImADgAAAEHAJ8AyQE2ABMAsABFWLAGLxuxBiA+WbAN3DAxAP//ADv/7QPIBoMAJgBYAAAABwG6ArIFff//AFv/5gUvBysCJgA5AAABBwClAO8BNwAJALAAL7Ad3DAxAP//AEr/6AQxBfUCJgBZAAABBgClWgEACQCwBy+wHtwwMQD//wBb/+YFLwbkAiYAOQAAAQcAcADpAToACQCwAC+wE9wwMQD//wBK/+gEMQWuAiYAWQAAAQYAcFQEAAkAsAcvsBTcMDEA//8AW//mBS8HHQImADkAAAEHAKEBHwE2AAkAsAAvsBbcMDEA//8ASv/oBDEF5wImAFkAAAEHAKEAigAAAAkAsAcvsBfcMDEA//8AW//mBS8HlQImADkAAAEHAKMBewFqAAwAsAAvsBzcsB/QMDH//wBK/+gEMQZfAiYAWQAAAQcAowDmADQADACwBy+wHdywINAwMf//AFv/5gVPBzUCJgA5AAABBwCmAWYBNgAMALAAL7AV3LAX0DAx//8ASv/oBLoF/wImAFkAAAEHAKYA0QAAAAwAsAcvsBbcsBjQMDEAAQBb/ogFMgWwACAAY7IHISIREjkAsABFWLAALxuxACA+WbAARViwFy8bsRcgPlmwAEVYsA0vG7ENGD5ZsABFWLASLxuxEhA+WbIEEgAREjmwDRCxCAOwCitYIdgb9FmwEhCxHAGwCitYIdgb9FkwMQEDBgYHBgcGFxY3FwYnIiY3NjcuAjcTMwMGFhcWNjcTBTKlF76VegoFOBs9DEVVV2kCAj2Q0mARpfalEnZ7h7QZpwWw/DOk9jhQWDkDAReQKwJtVFhICITfjAPO/DGLnAQEmpAD1AAAAQBK/lEEMQQ6ACMAebISJCUREjkAsABFWLAYLxuxGBw+WbAARViwIS8bsSEcPlmwAEVYsAsvG7ELEj5ZsABFWLAALxuxABA+WbAARViwEy8bsRMQPlmwCxCxBgOwCitYIdgb9FmwABCwENCwEC+yESEAERI5sBMQsR4BsAorWCHYG/RZMDEhFwcGBwYXFjcXBiciJjc2NzcGJy4CNxMzAwYXFhcWNxMzAwNcBS+DBwU4Gz0MRVVXaQIDsRJ7uWmLOwx17XYEAwpznWGI7bsDH1ZWOQMBF5ArAm1UlmdagwQCZLN5Arz9QSUjfAUGhAMK+8YA//8AtwAABzoHNwImADsAAAEHAJ4BvwE2ABMAsABFWLAMLxuxDCA+WbAP3DAxAP//AHcAAAX4BgECJgBbAAABBwCeAQIAAAATALAARViwCy8bsQscPlmwEdwwMQD//wChAAAFTQc3AiYAPQAAAQcAngC5ATYAEwCwAEVYsAEvG7EBID5ZsAvcMDEA////tf5FBBIGAQImAF0AAAEGAJ4WAAATALAARViwDy8bsQ8cPlmwFNwwMQD//wChAAAFTQcDAiYAPQAAAAcAagDvATb////lAAAE5wc2AiYAPgAAAQcAdQG5ATYAEwCwAEVYsAcvG7EHID5ZsAzcMDEA////5wAAA/EGAAImAF4AAAEHAHUBHwAAABMAsABFWLAHLxuxBxw+WbAM3DAxAP///+UAAATnBxcCJgA+AAABBwCiAZYBPwAJALAHL7AS3DAxAP///+cAAAPkBeECJgBeAAABBwCiAPwACQAJALAHL7AS3DAxAP///+UAAATnBzsCJgA+AAABBwCfAMwBNgAJALAHL7AO3DAxAP///+cAAAPuBgUCJgBeAAABBgCfMgAACQCwBy+wDtwwMQD///+HAAAHeAdCAiYAgQAAAQcAdQLqAUIAEwCwAEVYsAYvG7EGID5ZsBXcMDEA//8AD//oBnAGAQImAIYAAAEHAHUCawABAAkAsBkvsEHcMDEA//8AFf+hBZgHgAImAIMAAAEHAHUCIAGAABMAsABFWLAMLxuxDCA+WbAu3DAxAP//ACr/dwQzBf4CJgCJAAABBwB1ATP//gATALAARViwBS8bsQUcPlmwMdwwMQD///+XAAAEGgSNAiYCMAAAAQcCJv8E/24AOwCyHxoBcbJvGgFxsv8aAXGyDxoBcrKfGgFysl8aAXK2vxrPGt8aA3GyPxoBcbLfGgFdtB8aLxoCXTAxAP///5cAAAQaBI0CJgIwAAABBwIm/wT/bgA7ALIfGgFxsm8aAXGy/xoBcbIPGgFysp8aAXKyXxoBcra/Gs8a3xoDcbI/GgFxst8aAV20HxovGgJdMDEA//8AYgAABFoEjQImAdgAAAEGAiYlvgAIALIACwFdMDH///+bAAAEBQYeAiYCMwAAAQcARADSAB4AEwCwAEVYsAQvG7EEHj5ZsAzcMDEA////mwAABD8GHgImAjMAAAEHAHUBbQAeABMAsABFWLAFLxuxBR4+WbAN3DAxAP///5sAAAQIBh8CJgIzAAABBgCeaR4AEwCwAEVYsAQvG7EEHj5ZsA/cMDEA////mwAABEAGEwImAjMAAAEGAKV3HwAJALAEL7AV3DAxAP///5sAAAQiBesCJgIzAAAABwBqAJ8AHv///5sAAAQFBn0CJgIzAAABBwCjAQMAUgAMALAEL7AU3LAX0DAx////mwAABFEGmQImAjMAAAAHAicA+wAE//8AOf49BEkEowImAjEAAAAHAHkBYAAA//8ACgAAA/kGHgImAigAAAEHAEQAogAeABMAsABFWLAGLxuxBh4+WbAN3DAxAP//AAoAAAQPBh4CJgIoAAABBwB1AT0AHgATALAARViwBy8bsQcePlmwDtwwMQD//wAKAAAD+QYfAiYCKAAAAQYAnjkeABMAsABFWLAGLxuxBh4+WbAQ3DAxAP//AAoAAAP5BesCJgIoAAAABgBqbx7//wAYAAAB4AYeAiYB4wAAAQYARIoeABMAsABFWLACLxuxAh4+WbAF3DAxAP//ABgAAAL2Bh4CJgHjAAABBgB1JB4AEwCwAEVYsAMvG7EDHj5ZsAbcMDEA//8AGAAAAsAGHwImAeMAAAEHAJ7/IQAeABMAsABFWLACLxuxAh4+WbAI3DAxAP//ABgAAALaBesCJgHjAAAABwBq/1cAHv//AAoAAASoBhMCJgHeAAABBwClAJUAHwAJALAFL7AU3DAxAP//ADr/6gRjBh4CJgHdAAABBwBEAN8AHgATALAARViwCS8bsQkePlmwItwwMQD//wA6/+oEYwYeAiYB3QAAAQcAdQF6AB4ACQCwCS+wI9wwMQD//wA6/+oEYwYfAiYB3QAAAQYAnnYeAAkAsAkvsCLcMDEA//8AOv/qBGMGEwImAd0AAAEHAKUAhAAfAAkAsAkvsCvcMDEA//8AOv/qBGMF6wImAd0AAAAHAGoArAAe//8AOf/rBGoGHgImAdcAAAEHAEQAwAAeABMAsABFWLAJLxuxCR4+WbAT3DAxAP//ADn/6wRqBh4CJgHXAAABBwB1AVsAHgAJALAAL7AU3DAxAP//ADn/6wRqBh8CJgHXAAABBgCeVx4ACQCwAC+wE9wwMQD//wA5/+sEagXrAiYB1wAAAAcAagCNAB7//wBtAAAEgAYeAiYB0wAAAQcAdQE1AB4AEwCwAEVYsAEvG7EBHj5ZsAvcMDEA////mwAABCgFzAImAjMAAAEGAHBxIgATALAARViwBC8bsQQePlmwDNwwMQD///+bAAAEEwYFAiYCMwAAAQcAoQCnAB4ACQCwBC+wDtwwMQAAAv+b/lEEBQSNABcAGgCGshUbHBESObAVELAa0ACwAEVYsBUvG7EVHj5ZsABFWLALLxuxCxI+WbAARViwAC8bsQAQPlmwAEVYsBMvG7ETED5ZsABFWLABLxuxARA+WbALELEGA7AKK1gh2Bv0WbABELAQ0LAQL7IZFQAREjmwGS+xEQewCitYIdgb9FmyGhUAERI5MDEhFwcGBwYXFjcXBiciJjc2NychByMBMxMBIQMD0AUvgwcFOBs9DEVVV2kCA7ws/i6I+QKT2v39fAFIVwMfVlY5AwEXkCsCbVSYa+L5BI37cwGyAbj//wA5/+wESQYeAiYCMQAAAQcAdQFqAB4ACQCwCy+wH9wwMQD//wA5/+wESQYfAiYCMQAAAQYAnmYeAAkAsAsvsB7cMDEA//8AOf/sBEkF/wImAjEAAAEHAKIBRwAnAAkAsAsvsCXcMDEA//8AOf/sBEkGIwImAjEAAAEGAJ99HgAJALALL7Ah3DAxAP//AAoAAAQaBiMCJgIwAAABBgCf/h4AEwCwAEVYsAIvG7ECHj5ZsBvcMDEA//8ACgAAA/kFzAImAigAAAEGAHBBIgATALAARViwBi8bsQYePlmwDdwwMQD//wAKAAAD+QYFAiYCKAAAAQYAoXceAAkAsAYvsA/cMDEA//8ACgAAA/kF/wImAigAAAEHAKIBGgAnAAkAsAYvsBTcMDEAAAEACv5RA/kEjQAcAISyFR0eERI5ALAARViwFy8bsRcePlmwAEVYsBAvG7EQEj5ZsABFWLAELxuxBBA+WbAARViwFS8bsRUQPlmyHBcEERI5sBwvsQABsAorWCHYG/RZsBUQsQIBsAorWCHYG/RZsAPQsBAQsQsDsAorWCHYG/RZsBcQsRkBsAorWCHYG/RZMDEBIQMhByMXBwYHBhcWNxcGJyImNzY3IRMhByEDIQM1/ho2AjsjYAUvgwcFOBs9DEVVV2kCA5b+FcoDJSP9yS8B6AH4/srCAx9WVjkDAReQKwJtVIxgBI3E/vL//wAKAAAEDAYjAiYCKAAAAQYAn1AeABMAsABFWLAGLxuxBh4+WbAR3DAxAP//AD//8ARRBh8CJgHlAAABBgCeah4ACQCwCy+wItwwMQD//wA///AEUQYFAiYB5QAAAQcAoQCoAB4ACQCwCy+wJNwwMQD//wA///AEUQX/AiYB5QAAAQcAogFLACcACQCwCy+wKdwwMQD//wA//fkEUQSjAiYB5QAAAAcBugEg/pL//wAKAAAEqQYfAiYB5AAAAQYAnnweABMAsABFWLAHLxuxBx4+WbAQ3DAxAP//AA0AAAL4BhMCJgHjAAABBwCl/y8AHwAJALACL7AO3DAxAP//ABgAAALgBcwCJgHjAAABBwBw/ykAIgATALAARViwAi8bsQIePlmwBdwwMQD//wAYAAACywYFAiYB4wAAAQcAof9fAB4ACQCwAi+wB9wwMQD///+K/lEBzwSNAiYB4wAAAAYApOIA//8AGAAAAgIF/wImAeMAAAEGAKIBJwAJALACL7AM3DAxAP////L/6wSQBh8CJgHiAAABBwCeAPEAHgATALAARViwAC8bsQAePlmwE9wwMQD//wAK/gAEnQSNAiYB4QAAAAcBugDM/pn//wAKAAADNAYeAiYB4AAAAQYAdRseABMAsABFWLAFLxuxBR4+WbAI3DAxAP//AAr+AQM0BI0CJgHgAAAABwG6AMr+mv//AAoAAAM7BJACJgHgAAABBwG6AiUDigAQALAARViwCi8bsQoePlkwMf//AAoAAAM0BI0CJgHgAAAABwCiAO79Rv//AAoAAASoBh4CJgHeAAABBwB1AYsAHgATALAARViwCC8bsQgePlmwDNwwMQD//wAK/fkEqASNAiYB3gAAAAcBugEu/pL//wAKAAAEqAYjAiYB3gAAAQcAnwCeAB4AEwCwAEVYsAYvG7EGHj5ZsA/cMDEA//8AOv/qBGMFzAImAd0AAAEGAHB+IgAJALAJL7Ah3DAxAP//ADr/6gRjBgUCJgHdAAABBwChALQAHgAJALAJL7Ak3DAxAP//ADr/6gTkBh0CJgHdAAABBwCmAPsAHgAMALAJL7Aj3LAl0DAx//8ACgAABBYGHgImAdoAAAEHAHUBIAAeAAkAsAQvsBjcMDEA//8ACv4BBBYEjQImAdoAAAAHAboA0v6a//8ACgAABBYGIwImAdoAAAEGAJ8zHgAJALAEL7Aa3DAxAP//AA7/7QQbBh4CJgHZAAABBwB1AUkAHgAJALAJL7Ap3DAxAP//AA7/7QP/Bh8CJgHZAAABBgCeRR4ACQCwCS+wKNwwMQD//wAO/j0D/wSfAiYB2QAAAAcAeQFFAAD//wAO/+0EGAYjAiYB2QAAAQYAn1weAAkAsAkvsCvcMDEA//8AYv3/BFoEjQImAdgAAAAHAboA3v6Y//8AYgAABFoGIwImAdgAAAEGAJ9KHgATALAARViwBi8bsQYePlmwDdwwMQD//wBi/kMEWgSNAiYB2AAAAAcAeQEwAAb//wA5/+sEagYTAiYB1wAAAQYApWUfAAkAsAAvsBzcMDEA//8AOf/rBGoFzAImAdcAAAEGAHBfIgAJALAAL7AS3DAxAP//ADn/6wRqBgUCJgHXAAABBwChAJUAHgAJALAAL7AV3DAxAP//ADn/6wRqBn0CJgHXAAABBwCjAPEAUgAMALAAL7Ab3LAe0DAx//8AOf/rBMUGHQImAdcAAAEHAKYA3AAeAAwAsAAvsBTcsBbQMDEAAQA6/oEEagSNAB8AY7IFICEREjkAsABFWLAALxuxAB4+WbAARViwFi8bsRYePlmwAEVYsA0vG7ENGD5ZsABFWLASLxuxEhA+WbIEEgAREjmwDRCxCAOwCitYIdgb9FmwEhCxGwGwCitYIdgb9FkwMQEDBgYHBgcGFxY3FwYnIiY3NjcmJjcTMwMGFhcWNjcTBGqCGKeEeQoFOBs9DEVVV2kCAkuywhOB7IILW2drjhKDBI389Y3DKU9YOQMBF5ArAm1UYk0T3aoDAPz/ZXIDBG9pAwcA//8AjAAABh4GHwImAdUAAAEHAJ4BFQAeABMAsABFWLABLxuxAR4+WbAP3DAxAP//AG0AAASABh8CJgHTAAABBgCeMR4AEwCwAEVYsAgvG7EIHj5ZsA3cMDEA//8AbQAABIAF6wImAdMAAAAGAGpnHv///9YAAAQnBh4CJgHSAAABBwB1ATgAHgATALAARViwCC8bsQgePlmwDNwwMQD////WAAAEJwX/AiYB0gAAAQcAogEVACcACQCwBy+wEtwwMQD////WAAAEJwYjAiYB0gAAAQYAn0seABMAsABFWLAHLxuxBx4+WbAP3DAxAP///6QAAASuBkECJgAlAAAABgCuwQD///9eAAAEugZBAiYAKQAAAAcArv4wAAD///9mAAAFhwZBAiYALAAAAAcArv44AAD///9qAAACKAZDAiYALQAAAAcArv48AAL//wAY/+cFNQZBACYAMxQAAAcArv7qAAD///9YAAAFsQZBACYAPWQAAAcArv4qAAD//wAdAAAFCwZBACYAuhQAAAcArv70AAD//wAL//QDRgaaAiYAwwAAAQcAr/8j/+sAEgCwAC+wJ9ywDtCwJxCwEtAwMf///6QAAASuBbACBgAlAAD//wAnAAAEvAWwAgYAJgAA//8AJwAABLoFsAIGACkAAP///+UAAATnBbACBgA+AAD//wAnAAAFhwWwAgYALAAA//8ANQAAAigFsAIGAC0AAP//ACcAAAVxBbACBgAvAAD//wAnAAAGzgWwAgYAMQAA//8AJwAABYYFsAIGADIAAP//AGv/5wUhBcgCBgAzAAD//wAnAAAFBAWwAgYANAAA//8AnAAABSIFsAIGADgAAP//AKEAAAVNBbACBgA9AAD////DAAAFRwWwAgYAPAAA//8ANQAAAywHCgImAC0AAAAHAGr/qQE9//8AoQAABU0HAwImAD0AAAAHAGoA7wE2//8APv/qBDMGQQImALsAAAEHAK4BRgAAAAkAsBovsC7cMDEA//8AKP/qBAIGQQImAL8AAAEHAK4BEAAAAAkAsAgvsCrcMDEA//8AEf5hBAYGQQImAMEAAAEHAK4BGgAAAAkAsAMvsBXcMDEA//8Abv/0ApIGLAImAMMAAAEGAK4K6wAJALAAL7AQ3DAxAP//AFf/5QQ9BqICJgDLAAABBgCvGvMAEgCwCi+wMNywF9CwMBCwG9AwMf//ACEAAASNBDoCBgCOAAD//wA5/+gEJwRSAgYAUwAA////3f5gBFQEOgIGAHYAAP//AGQAAAQNBDoCBgBaAAAAAf+l/k0EaQRJAB4AWbITHyAREjkAsABFWLAFLxuxBRw+WbAARViwAC8bsQAcPlmwAEVYsBUvG7EVEj5ZsABFWLAQLxuxEBI+WbEMAbAKK1gh2Bv0WbAAELEbAbAKK1gh2Bv0WTAxExYWFxcBMwETFxYXFzcHBicmJicnASMBAyYmJwc3Nv5ihik1ASn8/iJxCxw7KSk6MyRYeCk6/rj/AgFeGEQwRQ9BBEcCepHMAcz9Gv5PI1YKAwPHDAIGjZ3f/gIDGQFzT0EDA7sSAP//AEz/9AL+BbgCJgDDAAAABwBq/3v/6///AFf/5QP+BcACJgDLAAAABgBqcvP//wA5/+gEJwZBAiYAUwAAAQcArgENAAAACQCwBC+wI9wwMQD//wBX/+UD/gY0AiYAywAAAQcArgEC//MACQCwCi+wGdwwMQD//wBU/+QGEAYyAiYAzgAAAQcArgIY//EACQCwGi+wLtwwMQD//wAnAAAEugcKAiYAKQAAAAcAagDwAT3//wAuAAAErAc9AiYAsQAAAQcAdQG5AT0AEwCwAEVYsAQvG7EEID5ZsAjcMDEAAAEAJP/qBLsFxwApAGSyAyorERI5ALAARViwCi8bsQogPlmwAEVYsB8vG7EfED5ZsgMfChESObAKELAO0LAKELESAbAKK1gh2Bv0WbADELEYAbAKK1gh2Bv0WbAfELAk0LAfELEnAbAKK1gh2Bv0WTAxATYnJyYmNz4CFx4CByc2JicmBgcGFxcWFgcOAicuAjcXBhYXFjYDTBazUeK+CQiZ+o2I1HAE9gdzdHWhDhS+S+W2CwqO+5eP6XwF9wiKgXihAX6QRh5P2I98vWYDA3HJgQFyfgMCcmF/SRtS3Zd7t2QCAXbRhQF8hgICav//ADUAAAIoBbACBgAtAAD//wA1AAADLAcKAiYALQAAAAcAav+pAT3//wAD/+cEYQWwAgYALgAA//8ALgAABXsFsAIGAiwAAP//ACcAAAVxBzYCJgAvAAABBwB1AaUBNgATALAARViwBS8bsQUgPlmwD9wwMQD//wCb/+cFUwckAiYA3gAAAQcAoQEVAT0ACQCwAS+wFNwwMQD///+kAAAErgWwAgYAJQAA//8AJwAABLwFsAIGACYAAP//AC4AAASsBbACBgCxAAD//wAnAAAEugWwAgYAKQAA//8AJwAABXwHJAImANwAAAEHAKEBVwE9AAkAsAAvsA3cMDEA//8AJwAABs4FsAIGADEAAP//ACcAAAWHBbACBgAsAAD//wBr/+cFIQXIAgYAMwAA//8ALgAABYMFsAIGALYAAP//ACcAAAUEBbACBgA0AAD//wBl/+gFDQXHAgYAJwAA//8AnAAABSIFsAIGADgAAP///8MAAAVHBbACBgA8AAD//wAi/+gD3ARQAgYARQAA//8AO//qBAIEUQIGAEkAAP//ABkAAARIBdoCJgDwAAABBwChAJz/8wAJALAAL7AN3DAxAP//ADn/6AQnBFICBgBTAAD////H/mAEDQRSAgYAVAAAAAEAOP/pA+4EUgAcAE2yAB0eERI5ALAARViwES8bsREcPlmwAEVYsAgvG7EIED5ZsQABsAorWCHYG/RZsgQRCBESObIVCBEREjmwERCxGAGwCitYIdgb9FkwMSUWNjc3DgInLgI3Nz4CFxYWFSM0JicmBgcCAehVgxLgC4XQcYvEWg8DEZXskLDS3ltWi6AGB60CZ1MBa7BiAwKM95gjnf+KBAThtF12BAT03v7z////tf5FBBIEOgIGAF0AAP///7kAAAQTBDoCBgBcAAD//wA7/+oEAgXNAiYASQAAAAYAamkA//8AGAAAA5gF8wImAOwAAAEHAHUAxv/zABMAsABFWLAELxuxBBw+WbAI3DAxAP//ABz/6QPEBFACBgBXAAD//wAfAAACCQXYAgYATQAA//8AIgAAAuEFxgImAI0AAAAHAGr/Xv/5////DP5GAf4F2AIGAE4AAP//ACIAAASBBfICJgDxAAABBwB1AUT/8gATALAARViwBC8bsQQcPlmwD9wwMQD///+1/kUEEgXnAiYAXQAAAQYAoVQAAAkAsAEvsBPcMDEA//8AtwAABzoHNgImADsAAAEHAEQCKAE2ABMAsABFWLALLxuxCyA+WbAO3DAxAP//AHcAAAX4BgACJgBbAAABBwBEAWsAAAATALAARViwCy8bsQscPlmwDtwwMQD//wC3AAAHOgc2AiYAOwAAAQcAdQLDATYAEwCwAEVYsAwvG7EMID5ZsA/cMDEA//8AdwAABfgGAAImAFsAAAEHAHUCBgAAABMAsABFWLAMLxuxDBw+WbAP3DAxAP//ALcAAAc6BwMCJgA7AAAABwBqAfUBNv//AHcAAAX4Bc0CJgBbAAAABwBqATgAAP//AKEAAAVNBzYCJgA9AAABBwBEASIBNgATALAARViwCC8bsQggPlmwCtwwMQD///+1/kUEEgYAAiYAXQAAAQYARH8AABMAsABFWLAPLxuxDxw+WbAR3DAxAP//AJAD/AGWBgADBgALAAAADACwBC+wAdCwAS8wMf//AKED9ALCBgADBgAGAAAAGwCwCS+wBtCwBi+wAdCwAS+wCRCwBNCwBC8wMQD//wA3/+8EPwWwACYABQAAAAcABQIfAAD///8P/kgC+wXjAiYAnAAAAQcAn/8//94ACQCwAC+wEdwwMQD//wCKBAAB/gYAAwYBhQAAAAYAsAQvMDH//wAnAAAGzgc2AiYAMQAAAQcAdQK+ATYAEwCwAEVYsAIvG7ECID5ZsBHcMDEA//8AEAAABmgGAAImAFEAAAEHAHUCmAAAAAkAsAMvsCTcMDEA////pP5sBK4FsAImACUAAAAHAKcBbwAD//8AIv5wA9wEUAImAEUAAAAHAKcAqQAH//8AJwAABLoHPQImACkAAAEHAEQBIwE9ABMAsABFWLAGLxuxBiA+WbAN3DAxAP//ACcAAAV8Bz0CJgDcAAABBwBEAYIBPQATALAARViwCC8bsQggPlmwC9wwMQD//wA7/+oEAgYAAiYASQAAAQcARACcAAAAEwCwAEVYsAkvG7EJHD5ZsCHcMDEA//8AGQAABEgF8wImAPAAAAEHAEQAx//zABMAsABFWLAILxuxCBw+WbAL3DAxAP//AHUAAAXXBbACBgC5AAD//wA//iIFigQ8AgYAzQAA//8AqAAABV4G/AImARkAAAEHAKwEVwEOABYAsABFWLAPLxuxDyA+WbAR3LAV0DAx//8AdwAABEQF0AImARoAAAEHAKwDwv/iABYAsABFWLAPLxuxDxw+WbAS3LAW0DAx//8AOf5FCIcEUgAmAFMAAAAHAF0EdQAA//8Aa/5FCXgFyAAmADMAAAAHAF0FZgAA//8AHv46BKgFxQImANsAAAAHAlEBdf+g//8AFv47A7wEUAImAO8AAAAHAlEBH/+h//8AZf4+BQ0FxwImACcAAAAHAlEBuP+k//8AOP4+A+4EUgImAEcAAAAHAlEBOv+k//8AoQAABU0FsAIGAD0AAP//AHf+XwQwBDoCBgC9AAD//wA1AAACKAWwAgYALQAA////pQAAB+AHJAImANoAAAEHAKECUAE9AAkAsAkvsBncMDEA////rQAABnIF2gImAO4AAAEHAKEBhf/zAAkAsAkvsBncMDEA//8ANQAAAigFsAIGAC0AAP///6QAAASuBx0CJgAlAAABBwChATABNgAJALAEL7AO3DAxAP//ACL/6AP0BecCJgBFAAABBwChAIgAAAAJALAYL7Av3DAxAP///6QAAASuBwMCJgAlAAAABwBqASgBNv//ACL/6AQDBc0CJgBFAAAABwBqAIAAAP///4cAAAd4BbACBgCBAAD//wAP/+gGcARSAgYAhgAA//8AJwAABLoHJAImACkAAAEHAKEA+AE9AAkAsAYvsA/cMDEA//8AO//qBAIF5wImAEkAAAEGAKFxAAAJALAJL7Aj3DAxAP//AEj/6AU3BtwCJgFYAAAABwBqAPcBD///ADb/6gP2BFACBgCdAAD//wA2/+oD9gXOAiYAnQAAAAYAanIB////pQAAB+AHCgImANoAAAAHAGoCSAE9////rQAABnIFwAImAO4AAAAHAGoBff/z//8AHv/tBKgHGAImANsAAAAHAGoA4wFL//8AFv/pA9oFzQImAO8AAAAGAGpXAP//ACcAAAV8BusCJgDcAAABBwBwASEBQQATALAARViwCC8bsQggPlmwC9wwMQD//wAZAAAESAWhAiYA8AAAAQYAcGb3ABMAsABFWLAHLxuxBxw+WbAL3DAxAP//ACcAAAV8BwoCJgDcAAAABwBqAU8BPf//ABkAAARIBcACJgDwAAAABwBqAJT/8///AGv/5wUhBwMCJgAzAAAABwBqAT8BNv//ADn/6AQnBc0CJgBTAAAABgBqfQD//wBi/+cFGgXIAgYBFwAA//8ANv/nBCYEUgIGARgAAP//AGL/5wUaBwcCJgEXAAAABwBqAU0BOv//ADb/5wQmBc0CJgEYAAAABgBqewD//wBP/+kE9wcZAiYA5wAAAAcAagEhAUz//wAj/+gD5QXNAiYA/wAAAAYAamIA//8Am//nBVMG6wImAN4AAAEHAHAA3wFBAAkAsAEvsBHcMDEA////tf5FBBIFrgImAF0AAAEGAHAeBAAJALABL7AQ3DAxAP//AJv/5wVTBwoCJgDeAAAABwBqAQ0BPf///7X+RQQSBc0CJgBdAAAABgBqTAD//wCb/+cFUwc8AiYA3gAAAQcApgFcAT0ADACwAS+wE9ywFdAwMf///7X+RQSEBf8CJgBdAAABBwCmAJsAAAAWALAARViwDy8bsQ8cPlmwFtywEtAwMf//AMUAAAVqBwoCJgDhAAAABwBqAUkBPf//AHAAAAQgBcACJgD5AAAABgBqbfP//wAuAAAGvQcLACYA5gsAACcALQSVAAAABwBqAfcBPv//ACIAAAXxBcAAJgD+AAAAJwCNBCYAAAAHAGoBcv/z//8AO//nBIgGAAIGAEgAAP///6T+lASuBbACJgAlAAAABwCtBOcAA///ACL+mAPcBFACJgBFAAAABwCtBCEAB////6QAAASuB7sCJgAlAAABBwCrBRUBPAAJALAEL7AZ3DAxAP//ACL/6APcBoUCJgBFAAABBwCrBG0ABgAJALAYL7A63DAxAP///6QAAAYYB7ECJgAlAAABBwI3AOsBIQAWALAARViwBS8bsQUgPlmwDtywFNAwMf//ACL/6AVwBnwCJgBFAAABBgI3Q+wAFgCwAEVYsBgvG7EYHD5ZsC/csDXQMDH///+kAAAErgeuAiYAJQAAAQcCOADyASsAFgCwAEVYsAQvG7EEID5ZsA7csBPQMDH//wAi/+gD8gZ5AiYARQAAAQYCOEr2ABYAsABFWLAYLxuxGBw+WbAt3LA00DAx////pAAABYAH3gImACUAAAEHAjkA7AETABYAsABFWLAFLxuxBSA+WbAM3LAS0DAx//8AIv/oBNgGqQImAEUAAAEGAjlE3gAWALAARViwGC8bsRgcPlmwLdywM9AwMf///6QAAASuB9UCJgAlAAABBwI6AOsBBQAWALAARViwBC8bsQQgPlmwDtywFdAwMf//ACL/6APsBqACJgBFAAABBgI6Q9AAFgCwAEVYsBgvG7EYHD5ZsC3csDbQMDH///+k/pQErgc3AiYAJQAAACcAngDyATYBBwCtBOcAAwATALAARViwBC8bsQQgPlmwD9wwMQD//wAi/pgD6QYBAiYARQAAACYAnkoAAQcArQQhAAcAEwCwAEVYsBgvG7EYHD5ZsDDcMDEA////pAAABK4HrgImACUAAAEHAjwBHAEwABYAsABFWLAELxuxBCA+WbAO3LAa0DAx//8AIv/oA+4GeQImAEUAAAEGAjx0+wAWALAARViwGC8bsRgcPlmwL9ywO9AwMf///6QAAASuB64CJgAlAAABBwI1ARwBMAAMALAEL7AO3LAa0DAx//8AIv/oA+4GeQImAEUAAAEGAjV0+wAMALAYL7Av3LA40DAx////pAAABK4IPgImACUAAAEHAj0BHAE2AAwAsAQvsA7csBjQMDH//wAi/+gD4gcIAiYARQAAAQYCPXQAAAwAsBgvsC/csDnQMDH///+kAAAErggXAiYAJQAAAQcCUAEgATwADACwBC+wDtywF9AwMf//ACL/6AP6BuECJgBFAAABBgJQeAYADACwGC+wL9ywONAwMf///6T+lASuBx0CJgAlAAAAJwChATABNgEHAK0E5wADABMAsABFWLAELxuxBCA+WbAO3DAxAP//ACL+mAP0BecCJgBFAAAAJwChAIgAAAEHAK0EIQAHABMAsABFWLAYLxuxGBw+WbAv3DAxAP//ACf+mwS6BbACJgApAAAABwCtBKgACv//ADv+kQQCBFECJgBJAAAABwCtBHYAAP//ACcAAAS6B8ICJgApAAABBwCrBN0BQwAJALAGL7Aa3DAxAP//ADv/6gQCBoUCJgBJAAABBwCrBFYABgAJALAJL7Au3DAxAP//ACcAAAS6BzICJgApAAABBwClAMgBPgAJALAGL7AW3DAxAP//ADv/6gQKBfUCJgBJAAABBgClQQEACQCwCS+wKtwwMQD//wAnAAAF4Ae4AiYAKQAAAQcCNwCzASgAFgCwAEVYsAcvG7EHID5ZsA/csBXQMDH//wA7/+oFWQZ8AiYASQAAAQYCNyzsABYAsABFWLAJLxuxCRw+WbAj3LAp0DAx//8AJwAABLoHtQImACkAAAEHAjgAugEyABYAsABFWLAGLxuxBiA+WbAP3LAU0DAx//8AO//qBAIGeQImAEkAAAEGAjgz9gAWALAARViwCS8bsQkcPlmwI9ywKNAwMf//ACcAAAVIB+UCJgApAAABBwI5ALQBGgAWALAARViwBi8bsQYgPlmwD9ywE9AwMf//ADv/6gTBBqkCJgBJAAABBgI5Ld4AFgCwAEVYsAkvG7EJHD5ZsCHcsCfQMDH//wAnAAAEugfcAiYAKQAAAQcCOgCzAQwAFgCwAEVYsAYvG7EGID5ZsA/csBbQMDH//wA7/+oEAgagAiYASQAAAQYCOizQABYAsABFWLAJLxuxCRw+WbAh3LAq0DAx//8AJ/6bBLoHPgImACkAAAAnAJ4AugE9AQcArQSoAAoAEwCwAEVYsAYvG7EGID5ZsBDcMDEA//8AO/6RBAIGAQImAEkAAAAmAJ4zAAEHAK0EdgAAABMAsABFWLAJLxuxCRw+WbAk3DAxAP//ADUAAALSB8ICJgAtAAABBwCrA5UBQwAJALACL7AS3DAxAP//ACIAAAKHBn4CJgCNAAABBwCrA0r//wAJALACL7AS3DAxAP/////+lwIoBbACJgAtAAAABwCtA18ABv///+T+mwIJBdgCJgBNAAAABwCtA0QACv//AGv+kQUhBcgCJgAzAAAABwCtBPYAAP//ADn+jwQnBFICJgBTAAAABwCtBIT//v//AGv/5wUhB7sCJgAzAAABBwCrBSwBPAAJALAKL7Ax3DAxAP//ADn/6AQnBoUCJgBTAAABBwCrBGoABgAJALAEL7Av3DAxAP//AGv/5wYvB7ECJgAzAAABBwI3AQIBIQAWALAARViwCi8bsQogPlmwJtywLNAwMf//ADn/6AVtBnwCJgBTAAABBgI3QOwAFgCwAEVYsAQvG7EEHD5ZsCTcsCrQMDH//wBr/+cFIQeuAiYAMwAAAQcCOAEJASsAFgCwAEVYsAovG7EKID5ZsCTcsCvQMDH//wA5/+gEJwZ5AiYAUwAAAQYCOEf2ABYAsABFWLAELxuxBBw+WbAk3LAp0DAx//8Aa//nBZcH3gImADMAAAEHAjkBAwETABYAsABFWLAKLxuxCiA+WbAk3LAq0DAx//8AOf/oBNUGqQImAFMAAAEGAjlB3gAWALAARViwBC8bsQQcPlmwItywKNAwMf//AGv/5wUhB9UCJgAzAAABBwI6AQIBBQAWALAARViwCi8bsQogPlmwJNywLdAwMf//ADn/6AQnBqACJgBTAAABBgI6QNAAFgCwAEVYsAQvG7EEHD5ZsCLcsCvQMDH//wBr/pEFIQc3AiYAMwAAACcAngEJATYBBwCtBPYAAAATALAARViwCi8bsQogPlmwJdwwMQD//wA5/o8EJwYBAiYAUwAAACYAnkcAAQcArQSE//4AEwCwAEVYsAQvG7EEHD5ZsCPcMDEA//8AW//oBiYHMwImAJgAAAEHAHUCBgEzABMAsABFWLAKLxuxCiA+WbAu3DAxAP//ADb/5gUFBgACJgCZAAABBwB1AVoAAAATALAARViwBC8bsQQcPlmwKtwwMQD//wBb/+gGJgczAiYAmAAAAQcARAFrATMAEwCwAEVYsAovG7EKID5ZsC3cMDEA//8ANv/mBQUGAAImAJkAAAEHAEQAvwAAABMAsABFWLAELxuxBBw+WbAp3DAxAP//AFv/6AYmB7gCJgCYAAABBwCrBSUBOQATALAARViwCi8bsQogPlmwOtwwMQD//wA2/+YFBQaFAiYAmQAAAQcAqwR5AAYAEwCwAEVYsAQvG7EEHD5ZsCjcMDEA//8AW//oBiYHKAImAJgAAAEHAKUBEAE0ABMAsABFWLAKLxuxCiA+WbAv3DAxAP//ADb/5gUFBfUCJgCZAAABBgClZAEAEwCwAEVYsAQvG7EEHD5ZsCvcMDEA//8AW/6RBiYGLgImAJgAAAAHAK0E4AAA//8ANv6IBQUEqAImAJkAAAAHAK0Edf/3//8AW/6RBS8FsAImADkAAAAHAK0EzAAA//8ASv6RBDEEOgImAFkAAAAHAK0EIgAA//8AW//mBS8HuwImADkAAAEHAKsFBAE8ABMAsABFWLAKLxuxCiA+WbAT3DAxAP//AEr/6AQxBoUCJgBZAAABBwCrBG8ABgATALAARViwCC8bsQgcPlmwFNwwMQD//wBb/+gGrQdCAiYAmgAAAQcAdQINAUIAEwCwAEVYsBovG7EaID5ZsB3cMDEA//8ASv/oBWEF7AImAJsAAAEHAHUBVf/sABMAsABFWLAWLxuxFhw+WbAe3DAxAP//AFv/6AatB0ICJgCaAAABBwBEAXIBQgATALAARViwEi8bsRIgPlmwHNwwMQD//wBK/+gFYQXsAiYAmwAAAQcARAC6/+wAEwCwAEVYsA4vG7EOHD5ZsB3cMDEA//8AW//oBq0HxwImAJoAAAEHAKsFLAFIABMAsABFWLASLxuxEiA+WbAb3DAxAP//AEr/6AVhBnECJgCbAAABBwCrBHT/8gATALAARViwDi8bsQ4cPlmwHNwwMQD//wBb/+gGrQc3AiYAmgAAAQcApQEXAUMAEwCwAEVYsBovG7EaID5ZsB7cMDEA//8ASv/oBWEF4QImAJsAAAEGAKVf7QATALAARViwFi8bsRYcPlmwH9wwMQD//wBb/ogGrQYCAiYAmgAAAAcArQTw//f//wBK/pEFYQSUAiYAmwAAAAcArQRrAAD//wCh/qEFTQWwAiYAPQAAAAcArQSkABD///+1/gwEEgQ6AiYAXQAAAAcArQUH/3v//wChAAAFTQe7AiYAPQAAAQcAqwTcATwACQCwAS+wF9wwMQD///+1/kUEEgaFAiYAXQAAAQcAqwQ5AAYACQCwAS+wHtwwMQD//wChAAAFTQcrAiYAPQAAAQcApQDHATcACQCwAS+wE9wwMQD///+1/kUEEgX1AiYAXQAAAQYApSQBAAkAsAEvsBrcMDEA////9P6uBRkGAAAmAEgAAAAnAiYB3QJCAQcAQwB7/20AEgCyLyEBXbIfIQFxsp8hAV0wMf//AJz+mgUiBbACJgA4AAAABwJRAjsAAP//AFP+mgQIBDoCJgD2AAAABwJRAdgAAP//AMX+mgVqBbACJgDhAAAABwJRAroAAP//AHD+mgQgBDsCJgD5AAAABwJRAbkAAP//AC7+mgSsBbACJgCxAAAABwJRAP8AAP//ABj+mgOJBDoCJgDsAAAABwJRAOUAAP//AGL+QwXBBcgCJgFMAAAABwJRAsf/qf////T+RgSDBFMCJgFNAAAABwJRAdf/rP//AA0AAAP5BgACBgBMAAAAAgAjAAAElAWwABIAGwB3shUcHRESObAVELAJ0ACwAEVYsA8vG7EPID5ZsABFWLAJLxuxCRA+WbISCQ8REjmwEi+xAAewCitYIdgb9FmyAw8JERI5sAMvsAAQsAvQsAzQsBIQsA3QsAkQsRUBsAorWCHYG/RZsAMQsRsBsAorWCHYG/RZMDEBIwcXFhYHBgQjIRMjNzM3MwczAQMFNjY3NiYnArHZIv7j/REQ/sf0/d2+ux67Ifci2v7EVgESga4PDnBrBEfEAQHvxND+BEeqv7/9x/4SAgKQd2l5BAAAAgAjAAAElAWwABIAGwB3shUcHRESObAVELAJ0ACwAEVYsA8vG7EPID5ZsABFWLAJLxuxCRA+WbISCQ8REjmwEi+xAAewCitYIdgb9FmyAw8JERI5sAMvsAAQsAvQsAzQsBIQsA3QsAkQsRUBsAorWCHYG/RZsAMQsRsBsAorWCHYG/RZMDEBIwcXFhYHBgQjIRMjNzM3MwczAQMFNjY3NiYnArHZIv7j/REQ/sf0/d2+ux67Ifci2v7EVgESga4PDnBrBEfEAQHvxND+BEeqv7/9x/4SAgKQd2l5BAAAAf/8AAAErAWwAA0ASwCwAEVYsAgvG7EIID5ZsABFWLACLxuxAhA+WbINCAIREjmwDS+xAAewCitYIdgb9FmwBNCwDRCwBtCwCBCxCgGwCitYIdgb9FkwMQEjAyMTIzczEyEHIQMzAofvdPZ0ph6lawOCJP11R+8Cn/1hAp+qAmfM/mUAAf/LAAADiQQ6AA0ASwCwAEVYsAgvG7EIHD5ZsABFWLACLxuxAhA+WbINCAIREjmwDS+xAAewCitYIdgb9FmwBNCwDRCwBtCwCBCxCgGwCitYIdgb9FkwMQEhAyMTIzczEyEHIQchAlb/AFHtUZ4enU4CtSP+OSwBAQHR/i8B0aoBv8T7AAABADcAAAWPBbAAFABwALAARViwBC8bsQQgPlmwAEVYsBIvG7ESID5ZsABFWLALLxuxCxA+WbAARViwCC8bsQgQPlmyExILERI5sBMvsBDQsQ0HsAorWCHYG/RZsAHQsgILEhESObACL7EKAbAKK1gh2Bv0WbIGCgIREjkwMQEjBzMBIQEBIQEjAyMTIzczNzMHMwLCzip9AgoBPv2YAYb+6P69rmz2vMcexiP2I88EP/MCZP07/RUCcP2QBD+qx8cAAAEAGQAABFkGAAAUAGwAsBIvsABFWLAELxuxBBw+WbAARViwCy8bsQsQPlmwAEVYsAgvG7EIED5ZshMSCxESObATL7EBB7AKK1gh2Bv0WbICCwQREjmwAi+xCgGwCitYIdgb9FmyBgoCERI5sAEQsA3QsBMQsBDQMDEBIwMzASEBASEDIwMjEyM3MzczBzMCpL5eXQFPASX+SQEY/v3eclLt0uEe4RvsG74Eu/3hAZ7+Bf3BAdn+JwS7qpubAAABAKEAAAVNBbAADgBXsgoPEBESOQCwAEVYsAgvG7EIID5ZsABFWLALLxuxCyA+WbAARViwAi8bsQIQPlmyBgIIERI5sAYvsQUHsAorWCHYG/RZsAHQsgoIAhESObAGELAO0DAxASMDIxMjNzMBIRMBIQEzA5nPWvhaxB59/vgBBc0BvAEe/e58AgT9/AIEqgMC/VACsPz+AAEAVP5fBDAEOgAOAGSyCg8QERI5ALAARViwCC8bsQgcPlmwAEVYsAsvG7ELHD5ZsABFWLACLxuxAhI+WbAARViwAC8bsQAQPlmwAEVYsAQvG7EEED5ZsQYHsAorWCHYG/RZsgoLABESObAN0LAO0DAxBSMDIxMjNzMDMxMBMwEzAt/VSe1IyB6inexmAWn+/iilAf5gAaCqA5H9BAL8/G8AAAH/wwAABUcFsAARAGQAsABFWLALLxuxCyA+WbAARViwDi8bsQ4gPlmwAEVYsAIvG7ECED5ZsABFWLAFLxuxBRA+WbIRCwIREjmwES+xAAewCitYIdgb9FmyBAsCERI5sAfQsBEQsAnQsg0LAhESOTAxASMBIQMBIQEjNzMBIRMBIQEzA8eKASP+7tz+fP7VAfF4HnT+7wEQ1gF6ASr+LHIClf1rAhX96wKVqgJx/fMCDf2PAAH/uQAABBMEOgARAGQAsABFWLALLxuxCxw+WbAARViwDi8bsQ4cPlmwAEVYsAIvG7ECED5ZsABFWLAFLxuxBRA+WbIRDgIREjmwES+xAAewCitYIdgb9FmyBA4CERI5sAfQsBEQsAnQsg0OAhESOTAxASMTIwMBIQEjNzMDMxMTIQEzAymW0/iX/vb+7AFngh6ExfiM/wEV/rCEAdf+KQFx/o8B16oBuf6eAWL+RwD//wAo/+oEAgRRAgYAvwAA////wwAABKcFsAImACoAAAAHAib/MP5p//8AgwJtBeMDMQBGAa+JAGZmQAD//wALAAAEPwXHAgYAFgAA//8AJv/oBDkFxQIGABcAAP//AAkAAAQqBbACBgAYAAD//wBa/+cEcwWwAgYAGQAA//8Ad//oBCcFuAAGABoUAP//AE//6ARZBcgABgAcFAD//wCO//kELwXIAAYAHQAA//8AdP/nBE4FyQAGABQUAP//AGv/6gUWB0sCJgArAAABBwB1AfUBSwAJALANL7Ak3DAxAP////f+TwRCBgACJgBLAAABBwB1AUIAAAAJALAEL7At3DAxAP//ACcAAAWGBzYCJgAyAAABBwBEAYMBNgATALAARViwBi8bsQYgPlmwC9wwMQD//wANAAAD+gYAAiYAUgAAAQcARAC5AAAAEwCwAEVYsAMvG7EDHD5ZsBTcMDEA////pAAABK4HIQImACUAAAEHAKwEigEzABYAsABFWLAELxuxBCA+WbAM3LAQ0DAx//8AIv/oA9wF7AImAEUAAAEHAKwD4v/+ABYAsABFWLAYLxuxGBw+WbAt3LAx0DAx//8AJwAABLoHKAImACkAAAEHAKwEUgE6ABYAsABFWLAGLxuxBiA+WbAN3LAR0DAx//8AO//qBAIF7AImAEkAAAEHAKwDy//+ABYAsABFWLAJLxuxCRw+WbAh3LAl0DAx////yQAAAr0HKAImAC0AAAEHAKwDCgE6ABYAsABFWLACLxuxAiA+WbAF3LAJ0DAx////fgAAAnIF5AImAI0AAAEHAKwCv//2ABYAsABFWLACLxuxAhw+WbAF3LAJ0DAx//8Aa//nBSEHIQImADMAAAEHAKwEoQEzABYAsABFWLAKLxuxCiA+WbAk3LAo0DAx//8AOf/oBCcF7AImAFMAAAEHAKwD3//+ABYAsABFWLAELxuxBBw+WbAi3LAm0DAx//8AJwAABNgHIQImADYAAAEHAKwEPAEzABYAsABFWLAELxuxBCA+WbAZ3LAd0DAx//8ABwAAAvsF7AImAFYAAAEHAKwDSP/+ABYAsABFWLAHLxuxBxw+WbAP3LAT0DAx//8AW//mBS8HIQImADkAAAEHAKwEeQEzABYAsABFWLAKLxuxCiA+WbAU3LAY0DAx//8ASv/oBDEF7AImAFkAAAEHAKwD5P/+ABYAsABFWLAILxuxCBw+WbAV3LAZ0DAx////JAAABWoGQQAmANBkAAAHAK799gAA//8AJ/6bBLwFsAImACYAAAAHAK0ElwAK//8AEP6IBA8GAAImAEYAAAAHAK0Epf/3//8AJ/6bBOAFsAImACgAAAAHAK0EcwAK//8AO/6RBIgGAAImAEgAAAAHAK0EkAAA//8AJ/4DBOAFsAImACgAAAAHAboBAf6c//8AO/35BIgGAAImAEgAAAAHAboBHv6S//8AJ/6bBYcFsAImACwAAAAHAK0FAAAK//8ADf6bA/kGAAImAEwAAAAHAK0EfwAK//8AJwAABXEHNgImAC8AAAEHAHUBpQE2AAkAsAQvsA/cMDEA//8AEQAABHUHPQImAE8AAAEHAHUBowE9AAkAsAQvsA/cMDEA//8AJ/7cBXEFsAImAC8AAAAHAK0E0QBL//8AEf7HBEoGAAImAE8AAAAHAK0EYAA2//8AJ/6bA8MFsAImADAAAAAHAK0ElwAK////5P6bAhcGAAImAFAAAAAHAK0DRAAK//8AJ/6bBs4FsAImADEAAAAHAK0FqwAK//8AEP6bBmgEUgImAFEAAAAHAK0FrgAK//8AJ/6XBYYFsAImADIAAAAHAK0FAgAG//8ADf6bA/oEUgImAFIAAAAHAK0EbAAK//8Aa//nBSEH3wImADMAAAEHAjYFGQFTAAwAsAovsCzcsDfQMDH//wAnAAAFBAdCAiYANAAAAQcAdQGrAUIACQCwAy+wFtwwMQD////H/mAEbQX3AiYAVAAAAQcAdQGb//cACQCwDS+wIdwwMQD//wAn/psE2AWwAiYANgAAAAcArQSYAAr////e/psC7wRTAiYAVgAAAAcArQM+AAr//wAk/pEEuwXHAiYANwAAAAcArQSwAAD//wAc/ogDxARQAiYAVwAAAAcArQRi//f//wCc/pQFIgWwAiYAOAAAAAcArQSfAAP//wA7/pECrgVBAiYAWAAAAAcArQP1AAD//wBb/+YFLwffAiYAOQAAAQcCNgTxAVMADACwAC+wHNywJ9AwMf//AJsAAAWBBzcCJgA6AAABBwClAN0BQwAJALABL7AR3DAxAP//AGQAAAQNBewCJgBaAAABBgClFvgACQCwAS+wEdwwMQD//wCb/psFgQWwAiYAOgAAAAcArQTVAAr//wBk/psEDQQ6AiYAWgAAAAcArQRCAAr//wC3/psHOgWwAiYAOwAAAAcArQXFAAr//wB3/psF+AQ6AiYAWwAAAAcArQUnAAr////l/psE5wWwAiYAPgAAAAcArQSfAAr////n/psD5AQ6AiYAXgAAAAcArQRDAAr///6z/+cFZwXYACYAM0YAAAcBcf3HAAD///+bAAAEBQUeAiYCMwAAAAcArv9I/t3///9tAAAENQUhACYCKDwAAAcArv4//uD///94AAAE5QUcACYB5DwAAAcArv5K/tv///97AAACCwUhACYB4zwAAAcArv5N/uD////S/+oEbQUeACYB3QoAAAcArv6k/t3///8sAAAEvAUeACYB0zwAAAcArv3+/t3////iAAAEggUeACYB8woAAAcArv60/t3///+bAAAEBQSNAgYCMwAA//8ACgAABAAEjQIGAjIAAP//AAoAAAP5BI0CBgIoAAD////WAAAEJwSNAgYB0gAA//8ACgAABKkEjQIGAeQAAP//ABgAAAHPBI0CBgHjAAD//wAKAAAEnQSNAgYB4QAA//8ACgAABcgEjQIGAd8AAP//AAoAAASoBI0CBgHeAAD//wA6/+oEYwShAgYB3QAA//8ACgAABDYEjQIGAdwAAP//AGIAAARaBI0CBgHYAAD//wBtAAAEgASNAgYB0wAA////pAAABIAEjQIGAdQAAP//ABgAAALaBesCJgHjAAAABwBq/1cAHv//AG0AAASABesCJgHTAAAABgBqZx7//wAKAAAD+QXrAiYCKAAAAAYAam8e//8ACgAAA/4GHgImAeoAAAEHAHUBLAAeAAkAsAQvsAjcMDEA//8ADv/tA/8EnwIGAdkAAP//ABgAAAHPBI0CBgHjAAD//wAYAAAC2gXrAiYB4wAAAAcAav9XAB7////y/+sDsASNAgYB4gAA//8ACgAABJ0GHgImAeEAAAEHAHUBIAAeAAkAsAQvsA/cMDEA//8Acv/oBIIGBQImAgEAAAEHAKEAiAAeAAkAsA8vsBPcMDEA////mwAABAUEjQIGAjMAAP//AAoAAAQABI0CBgIyAAD//wAKAAAD3wSNAgYB6gAA//8ACgAAA/kEjQIGAigAAP//AAsAAASuBgUCJgH+AAABBwChAMYAHgAJALAAL7AN3DAxAP//AAoAAAXIBI0CBgHfAAD//wAKAAAEqQSNAgYB5AAA//8AOv/qBGMEoQIGAd0AAP//AAoAAASkBI0CBgHvAAD//wAKAAAENgSNAgYB3AAA//8AOf/sBEkEowIGAjEAAP//AGIAAARaBI0CBgHYAAD///+kAAAEgASNAgYB1AAAAAEADf45A+4EoAAoALOyIikqERI5ALAYL7AARViwDC8bsQwePlmwAEVYsBcvG7EXED5ZsAwQsQYBsAorWCHYG/RZsigXDBESObAoL7K/KAFytK8ovygCXbRvKH8oAnGy/ygBcbIPKAFysl8oAXKyzygBcbI/KAFxtB8oLygCXbKPKAFyskooAV2yCSgGERI5sSYBsAorWCHYG/RZshEmKBESObAXELAa0LAXELEhAbAKK1gh2Bv0WbIeJiEREjkwMQEyNjc2JiIGBwc2NhcWFgcGBxYWBwYGBwMjEyYmNzMWFjMWNjc2Jyc3AgRmgAoKZbBqD+4M/cLD3ggK6VFaBAfYtk3uT4aGAuoCXFZqkAwV3IcgAqpTTURMRT4BmLICA6aNtWUjhlmOtRT+RAHII6p5R0wDWU+gAQGwAAABAAr+mgS9BI0ADwCqsgMQERESOQCwAEVYsAwvG7EMHj5ZsABFWLAJLxuxCR4+WbAARViwAS8bsQEYPlmwAEVYsAYvG7EGED5ZsABFWLADLxuxAxA+WbIKBgkREjmwCi+0rwq/CgJdsj8KAXGyzwoBcbI/CgFysv8KAXGyDwoBcrRvCn8KAnG03wrvCgJdtB8KLwoCXbJfCgFysQUBsAorWCHYG/RZsAMQsQ4HsAorWCHYG/RZMDEBIxMjEyEDIxMzAyETMwMzBF/uPr1S/gZT7crtVgH7Vu2rv/6aAWYB2/4lBI3+EQHv/CgAAAEAOv5DBE8EowAeAGCyAx8gERI5ALAARViwDS8bsQ0ePlmwAEVYsAQvG7EEEj5ZsABFWLADLxuxAxA+WbIAAw0REjmwBtCyEQ0DERI5sA0QsRQBsAorWCHYG/RZsAMQsRwBsAorWCHYG/RZMDEBBgYHAyMTJgI3NxIAFxYWFycmJicmBgcGFxQWFxY3BAIZ6KxL7k6blRcGIAFB6cLiCusDYGuFsBoQAWRh4zgBhafUFf5OAcEvASjFNAEOAUEGBN29AWdwBAXAtIk/cH8ECNoA//8AbQAABIAEjQIGAdMAAP//ADf+OgWlBKcCJgIXAAAABwJRAr//oP//AAsAAASuBcwCJgH+AAABBwBwAJAAIgAJALAAL7AK3DAxAP//AHL/6ASCBcwCJgIBAAABBgBwUiIACQCwDy+wENwwMQD//wBDAAAFNwSOAgYB8QAA//8AGP/rBacEjQAmAeMAAAAHAeIB9wAA////ggAABgwGAAImAnMAAAAHAHUCfAAA////3P/JBLoGHgImAnUAAAAHAHUBegAe//8ADv35A/8EnwImAdkAAAAHAboA8/6S//8AjAAABh4GHgImAdUAAAAHAEQBfgAe//8AjAAABh4GHgImAdUAAAAHAHUCGQAe//8AjAAABh4F6wImAdUAAAAHAGoBSwAe//8AbQAABIAGHgImAdMAAAAHAEQAmgAe////pP5UBK4FsAImACUAAAAHAKQBbQAD//8AIv5YA9wEUAImAEUAAAAHAKQApwAH//8AJ/5bBLoFsAImACkAAAAHAKQBLgAK//8AO/5RBAIEUQImAEkAAAAHAKQA/AAA////m/5RBAUEjQImAjMAAAAHAKQBEAAA//8ACv5ZA/kEjQImAigAAAAHAKQA3gAI////5P6bAcsEOgImAI0AAAAHAK0DRAAKAAAAAAAcAVYAAQAAAAAAAAAvAAAAAQAAAAAAAQANAC8AAQAAAAAAAgAGADwAAQAAAAAAAwAUAEIAAQAAAAAABAAUAEIAAQAAAAAABQATAFYAAQAAAAAABgATAGkAAQAAAAAABwAgAHwAAQAAAAAACQAGAJwAAQAAAAAACwAKAKIAAQAAAAAADAATAKwAAQAAAAAADQAuAL8AAQAAAAAADgAqAO0AAwABBAkAAABeARcAAwABBAkAAQAaAXUAAwABBAkAAgAMAY8AAwABBAkAAwAoAZsAAwABBAkABAAoAZsAAwABBAkABQAmAcMAAwABBAkABgAmAekAAwABBAkABwBAAg8AAwABBAkACQAMAk8AAwABBAkACwAUAlsAAwABBAkADAAmAm8AAwABBAkADQBcApUAAwABBAkADgBUAvEAAwABBAkAEAAMA0UAAwABBAkAEQAaA1FDb3B5cmlnaHQgMjAxMSBHb29nbGUgSW5jLiBBbGwgUmlnaHRzIFJlc2VydmVkLlJvYm90byBNZWRpdW1JdGFsaWNSb2JvdG8gTWVkaXVtIEl0YWxpY1ZlcnNpb24gMi4xMzc7IDIwMTdSb2JvdG8tTWVkaXVtSXRhbGljUm9ib3RvIGlzIGEgdHJhZGVtYXJrIG9mIEdvb2dsZS5Hb29nbGVHb29nbGUuY29tQ2hyaXN0aWFuIFJvYmVydHNvbkxpY2Vuc2VkIHVuZGVyIHRoZSBBcGFjaGUgTGljZW5zZSwgVmVyc2lvbiAyLjBodHRwOi8vd3d3LmFwYWNoZS5vcmcvbGljZW5zZXMvTElDRU5TRS0yLjAAQwBvAHAAeQByAGkAZwBoAHQAIAAyADAAMQAxACAARwBvAG8AZwBsAGUAIABJAG4AYwAuACAAQQBsAGwAIABSAGkAZwBoAHQAcwAgAFIAZQBzAGUAcgB2AGUAZAAuAFIAbwBiAG8AdABvACAATQBlAGQAaQB1AG0ASQB0AGEAbABpAGMAUgBvAGIAbwB0AG8AIABNAGUAZABpAHUAbQAgAEkAdABhAGwAaQBjAFYAZQByAHMAaQBvAG4AIAAyAC4AMQAzADcAOwAgADIAMAAxADcAUgBvAGIAbwB0AG8ALQBNAGUAZABpAHUAbQBJAHQAYQBsAGkAYwBSAG8AYgBvAHQAbwAgAGkAcwAgAGEAIAB0AHIAYQBkAGUAbQBhAHIAawAgAG8AZgAgAEcAbwBvAGcAbABlAC4ARwBvAG8AZwBsAGUARwBvAG8AZwBsAGUALgBjAG8AbQBDAGgAcgBpAHMAdABpAGEAbgAgAFIAbwBiAGUAcgB0AHMAbwBuAEwAaQBjAGUAbgBzAGUAZAAgAHUAbgBkAGUAcgAgAHQAaABlACAAQQBwAGEAYwBoAGUAIABMAGkAYwBlAG4AcwBlACwAIABWAGUAcgBzAGkAbwBuACAAMgAuADAAaAB0AHQAcAA6AC8ALwB3AHcAdwAuAGEAcABhAGMAaABlAC4AbwByAGcALwBsAGkAYwBlAG4AcwBlAHMALwBMAEkAQwBFAE4AUwBFAC0AMgAuADAAUgBvAGIAbwB0AG8ATQBlAGQAaQB1AG0AIABJAHQAYQBsAGkAYwAAAAADAAD/9AAA/2oAZAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAgAIAAL//wAPAAEAAgAOAAAAAAAAAigAAgBZACUAPgABAEUAXgABAHkAeQABAIEAgQABAIMAgwABAIYAhgABAIkAiQABAIsAlgABAJgAnQABAKQApAABAKgArQADALEAsQABALoAuwABAL8AvwABAMEAwQABAMMAwwABAMcAxwABAMsAywABAM0AzgABANAA0QABANMA0wABANoA3gABAOEA4QABAOUA5QABAOcA6QABAOsA+wABAP0A/QABAP8BAQABAQMBAwABAQgBCQABARYBGgABARwBHAABASABIgABASQBJQADASoBKwABATMBNAABATYBNgABATsBPAABAUEBRAABAUcBSAABAUsBTQABAVEBUQABAVQBWAABAV0BXgABAWIBYgABAWQBZAABAWgBaAABAWoBbAABAW4BbgABAXABcAABAboBugADAbsBwQACAdIB5gABAeoB6gABAfMB8wABAfUB9QABAfwB/gABAgACAQABAgMCAwABAgcCBwABAgkCCwABAhECEQABAhYCGAABAhoCGgABAigCKAABAisCKwABAi0CLQABAjACMwABAl8CYwABAnoC4gABAuUDiwABA40DpAABA6YDsgABA7QDvQABA78D2gABA94D3gABA+AD5wABA+kD6wABA+4D8gABA/QEfAABBH8EfwABBIIEgwABBIUEhgABBIgEiwABBJUE0AABBNIE8QABBPME+gABBPwE/QABBQcFDQABAAEAAgAAAAwAAAAsAAEADgCoAKgAqQCpAKoAqgCrAKsArACsASQBJQEmAScAAQAFAHkApACtAK0BugAAAAEAAAAKADIATAAEREZMVAAaY3lybAAaZ3JlawAabGF0bgAaAAQAAAAA//8AAgAAAAEAAmNwc3AADmtlcm4AFAAAAAEAAAAAAAEAAQACAAYCEAABAAAAAQAIAAEACgAFACQASAABAPoACAAKABQAFQAWABcAGAAZABoAGwAcAB0AJQAmACcAKAApACoAKwAsAC0ALgAvADAAMQAyADMANAA1ADYANwA4ADkAOgA7ADwAPQA+AGUAZwCBAIMAhACMAI8AkQCTALEAsgCzALQAtQC2ALcAuAC5ALoA0gDTANQA1QDWANcA2ADZANoA2wDcAN0A3gDfAOAA4QDiAOMA5ADlAOYA5wDoAOkBLwEzATUBNwE5ATsBQQFDAUUBSQFLAUwBWAFZAZcBnQGiAaUCegJ7An0CfwKAAoECggKDAoQChQKGAocCiAKJAooCiwKMAo0CjgKPApACkQKSApMClAKVApYClwKYApkCtgK4AroCvAK+AsACwgLEAsYCyALKAswCzgLQAtIC1ALWAtgC2gLcAt4C4ALiAuMC5QLnAukC6wLtAu8C8QLzAvUC+AL6AvwC/gMAAwIDBAMGAwgDCgMMAw4DEAMSAxQDFgMYAxoDHAMeAyADIgMkAyUDJwMpAysDLQOGA4cDiAOJA4oDiwOMA44DjwOQA5EDkgOTA5QDlQOWA5cDmAOZA5oDmwOcA50DrQOuA68DsAOxA7IDswO0A7UDtgO3A7gDuQO6A7sDvAO9A74DvwPAA8EDwgPTA9UD1wPZA+4D8APyBAcEDQQTBH0EggSGBQcFCQACAAAAAgAKQXoAAQPuAAQAAAHyB9A8GDwYB/4IYD5QPwg8HkEuPtQIZj92P3Y/Ej9gP3Y/dkEuP6IMBAzSP+xAvkD2PDA92kEUDUg+sj/APNoNjg7EDs4/mD+YPuI/wD/aD9BA3BA2PIpA3BBQP8BBLhCWPRg+UEEuPlARGBIWExgT+hScQNwUoj+YF4gZehqYGrIauBq+Hbgdvh34Hi4euCBCIgwj4j92JTAm2jwwKTw/dj92PJA/dj92P3YqEivAP3Y87iyqLXAuAi5kL0o85C/cPIowsjDcMrY/wDZANn43vDmKP8A4RjjUOP45VDmKPlA+4kC+QNw5sD/APRg85DwwPIo/Ej8SPxI/djwwPIo/dj92QS485DwwPIo8GDnaPBg8GDwYQWo7ZDuyQWQ8DkFMQVJBZEFSQUxBTEFMQUw8AEFSPB5BLkEuQS5BLj/sPlA+UD5QPlA+UD5QPlA8Hj7UPtQ+1D7UP3Y/dj92P3Y/dkEuQS5BLkEuQS492j6yPrI+sj6yPrI+sj6yPNo82jzaPNo/mD7iPuI+4j7iPuJA3EDcPlA+sj5QPrI+UD6yPB48HjwePB5BLj7UPNo+1DzaPtQ82j7UPNo+1DzaP3Y/mD92P3Y/dj92P3Y/Ej9gP2A/YD9gP3Y/mD92P5g/dj+YP5hBLj7iQS4+4kEuPuI/2j/aP9o/7D/sP+xA9j3aQNw92kEUQRRBFEFkQWRBakFSQVJBUkFSQVJBUkFSQWRBZEFkQWRBZEFSQVJBUkFkQUw8DjwOPA48DkFkQWRBZEFqPlA+1D92P3ZBLj3aPlA/CD7UQRQ/dj92PxI/dj92QS4/oj/sPdo8MD92Pdo/mD7iQNw+4j7UPRg/dj92PxI/EjyQPlA/CD0YPtQ/dj92QS4/ojweP+w8MD6yPNo+4j/AQNw8ijzaPORA3ED2QPZA9j3aQNw8GDwYPBg/dj+YPlA+sj7UPNpAvkDcPB492kDcP3Y8MDyKP3Y+UD6yPlA+sj7UPNo82jzaPDA8ikEuPuI+4j/APJBA3DyQQNw8kEDcPlA+sj5QPrI+UD6yPlA+sj5QPrI+UD6yPlA+sj5QPrI+UD6yPlA+sj5QPrI+UD6yPtQ82j7UPNo+1DzaPtQ82j7UPNo+1DzaPtQ82j7UPNo/dj92QS4+4kEuPuJBLj7iQS4+4kEuPuJBLj7iQS4+4j7iPdpA3D3aQNw92kDcP+w9GDzkP5g87j0YPxI92j92P5g+UD6yPtQ/dkEuPuI/2j8IP8BBLkEuP3Y/mD8SPxI/YD92P5g/dj+YQS4/oj/AP9o/7EC+QNxAvkDcQPZBFEEuQVJBZEFSQUxBakFMQVJBZEFqAAIApQAEAAQAAAAGAAYAAQALAAwAAgATABMABAAlACoABQAsAC0ACwAvADYADQA4ADgAFQA6AD8AFgBFAEYAHABJAEoAHgBMAEwAIABPAE8AIQBRAFQAIgBWAFYAJgBYAFgAJwBaAF0AKABfAF8ALACKAIoALQCWAJYALgCdAJ0ALwCxALUAMAC3ALkANQC7ALsAOAC9AL0AOQDAAMEAOgDDAMMAPADFAMUAPQDHAM4APgDSANIARgDUAN4ARwDgAO8AUgDxAPEAYgD2APgAYwD7APwAZgD+AQAAaAEDAQUAawEKAQoAbgENAQ0AbwEYARoAcAEiASIAcwEuATAAdAEzATUAdwE3ATcAegE5ATkAewE7ATsAfAFDAUQAfQFUAVQAfwFWAVYAgAFYAVgAgQFcAV4AggGEAYUAhQGHAYkAhwHYAdgAigHaAdsAiwHdAd0AjQHgAeEAjgHrAe0AkAH/Af8AkwIOAhAAlAIwAjAAlwIzAjMAmAJFAkUAmQJHAkgAmgJ6AnsAnAJ9An0AngJ/ApQAnwKZAqAAtQKiAqUAvQKqAq8AwQK0ArwAxwK+Ar4A0ALAAsAA0QLCAsIA0gLEAsQA0wLGAs8A1ALYAtoA3gLcAtwA4QLeAt4A4gLgAuAA4wLiAuIA5ALnAucA5QLpAukA5gLrAusA5wLtAu0A6ALvAu8A6QLxAv0A6gL/Av8A9wMBAwEA+AMDAwMA+QMOAw4A+gMQAxAA+wMSAxIA/AMgAyAA/QMiAyUA/gMnAycBAgMpAykBAwMvAzgBBANDA0cBDgNNA08BEwNUA1QBFgNlA2kBFwNtA28BHAN4A3gBHwOGA4sBIAOOA50BJgOgA6ABNgOkA6QBNwOmA6YBOAOqA6oBOQOtA64BOgOwA7EBPAOzA7kBPgO7A70BRQO/A8QBSAPGA8cBTgPJA8wBUAPSA9MBVAPVA9UBVgPXA9cBVwPZA9wBWAPfA+QBXAPmA+YBYgPqA+sBYwPwA/ABZQPyA/sBZgP+A/8BcAQBBAQBcgQLBAwBdgQQBBABeAQSBBgBeQQeBEYBgARIBEgBqQRKBFcBqgRfBF8BuARwBHUBuQR3BHcBvwR7BHwBwAR/BH8BwgSBBIIBwwSEBIQBxQSGBIYBxgSXBJsBxwSdBJ0BzASfBKABzQSiBKIBzwSmBKgB0ASqBKoB0wSsBK4B1ASwBLAB1wSyBLIB2AS0BLoB2QS8BLwB4AS/BL8B4QTCBMYB4gTIBMgB5wTKBMsB6ATPBM8B6gTSBNIB6wTYBNgB7ATdBN0B7QToBOgB7gTqBOoB7wTxBPEB8AT1BPUB8QALADj/xADS/8QA1v/EATn/xAFF/8QDDv/EAxD/xAMS/8QDwf/EBHf/xAS//8QAGAA6ABQAOwAmAD0AFgEZABQCmQAWAyAAJgMiABYDJAAWA4sAFgOaABYDnQAWA9MAJgPVACYD1wAmA9kAFgPqABQD8gAWBHAAFgRyABYEdAAWBIYAFgTCABQExAAUBMYAJgABABP/CADnABD+7gAS/u4AJf9AAC7/MAA4ABQARf/eAEf/6wBI/+sASf/rAEv/6wBT/+sAVf/rAFb/5gBZ/+oAWv/oAF3/6ACU/+sAmf/rAJv/6gCy/0AAtP9AALv/6wC9/+gAyP/rAMn/6wDL/+oA0gAUANYAFAD3/+sBA//rAQ3/QAEY/+sBGv/oAR7/6wEi/+sBOQAUAUL/6wFFABQBYP/rAWH/6wFr/+sBhv7uAYr+7gGO/u4Bj/7uAev/wAHt/8ACM//AAn//QAKA/0ACgf9AAoL/QAKD/0AChP9AAoX/QAKa/94Cm//eApz/3gKd/94Cnv/eAp//3gKg/94Cof/rAqL/6wKj/+sCpP/rAqX/6wKr/+sCrP/rAq3/6wKu/+sCr//rArD/6gKx/+oCsv/qArP/6gK0/+gCtf/oArb/QAK3/94CuP9AArn/3gK6/0ACu//eAr3/6wK//+sCwf/rAsP/6wLF/+sCx//rAsn/6wLL/+sCzf/rAs//6wLR/+sC0//rAtX/6wLX/+sC5f8wAvn/6wL7/+sC/f/rAw4AFAMQABQDEgAUAxX/6gMX/+oDGf/qAxv/6gMd/+oDH//qAyP/6AMy/8ADM//AAzT/wAM1/8ADNv/AAzf/wAM4/8ADTf/AA07/wANP/8ADhv9AA47/QAOe/+sDov/qA6T/6wOm/+gDqf/qA6r/6wOr/+oDsv8wA7b/QAPBABQDw//eA8T/6wPG/+sDyP/rA8n/6APL/+sD0v/oA9r/6APi/0AD4//eA+b/6wPr/+gD7P/rA/H/6wPz/+gD+P9AA/n/3gP6/0AD+//eA///6wQB/+sEAv/rBAz/6wQO/+sEEP/rBBT/6AQW/+gEGP/oBB3/6wQe/0AEH//eBCD/QAQh/94EIv9ABCP/3gQk/0AEJf/eBCb/QAQn/94EKP9ABCn/3gQq/0AEK//eBCz/QAQt/94ELv9ABC//3gQw/0AEMf/eBDL/QAQz/94ENP9ABDX/3gQ3/+sEOf/rBDv/6wQ9/+sEP//rBEH/6wRD/+sERf/rBEv/6wRN/+sET//rBFH/6wRT/+sEVf/rBFf/6wRZ/+sEW//rBF3/6wRf/+sEYf/rBGP/6gRl/+oEZ//qBGn/6gRr/+oEbf/qBG//6gRx/+gEc//oBHX/6AR3ABQEmf9ABJr/3gSc/+sEoP/rBKT/6gSp/+sEq//rBL8AFATD/+gExf/oBMv/wATS/8AE6v/AADMAOP/fADr/5AA7/+wAPf/dANL/3wDW/98BGf/kATn/3wFF/98B6wAOAe0ADgIzAA4Cmf/dAw7/3wMQ/98DEv/fAyD/7AMi/90DJP/dAzIADgMzAA4DNAAOAzUADgM2AA4DNwAOAzgADgNNAA4DTgAOA08ADgOL/90Dmv/dA53/3QPB/98D0//sA9X/7APX/+wD2f/dA+r/5APy/90EcP/dBHL/3QR0/90Ed//fBIb/3QS//98Ewv/kBMT/5ATG/+wEywAOBNIADgTqAA4AHQA4/84AOv/tAD3/0ADS/84A1v/OARn/7QE5/84BRf/OApn/0AMO/84DEP/OAxL/zgMi/9ADJP/QA4v/0AOa/9ADnf/QA8H/zgPZ/9AD6v/tA/L/0ARw/9AEcv/QBHT/0AR3/84Ehv/QBL//zgTC/+0ExP/tABEALv/uADn/7gKV/+4Clv/uApf/7gKY/+4C5f/uAxT/7gMW/+4DGP/uAxr/7gMc/+4DHv/uA7L/7gRi/+4EZP/uBMH/7gBNAAYAEAALABAADQAUAEEAEgBH/+gASP/oAEn/6ABL/+gAVf/oAGEAEwCU/+gAmf/oALv/6ADI/+gAyf/oAPf/6AED/+gBHv/oASL/6AFC/+gBYP/oAWH/6AFr/+gBhAAQAYUAEAGHABABiAAQAYkAEAKh/+gCov/oAqP/6AKk/+gCpf/oAr3/6AK//+gCwf/oAsP/6ALF/+gCx//oAsn/6ALL/+gCzf/oAs//6ALR/+gC0//oAtX/6ALX/+gDnv/oA8T/6API/+gDy//oA9sAEAPcABAD3wAQA+b/6APs/+gD8f/oA///6AQB/+gEAv/oBA7/6AQd/+gEN//oBDn/6AQ7/+gEPf/oBD//6ARB/+gEQ//oBEX/6ARZ/+gEW//oBF3/6ARh/+gEnP/oBKn/6ASr/+gAAgD2/9YBhf+YAEAAR//sAEj/7ABJ/+wAS//sAFX/7ACU/+wAmf/sALv/7ADI/+wAyf/sAPf/7AED/+wBHv/sASL/7AFC/+wBYP/sAWH/7AFr/+wCof/sAqL/7AKj/+wCpP/sAqX/7AK9/+wCv//sAsH/7ALD/+wCxf/sAsf/7ALJ/+wCy//sAs3/7ALP/+wC0f/sAtP/7ALV/+wC1//sA57/7APE/+wDyP/sA8v/7APm/+wD7P/sA/H/7AP//+wEAf/sBAL/7AQO/+wEHf/sBDf/7AQ5/+wEO//sBD3/7AQ//+wEQf/sBEP/7ARF/+wEWf/sBFv/7ARd/+wEYf/sBJz/7ASp/+wEq//sABkAU//iARj/4gGFABgCq//iAqz/4gKt/+ICrv/iAq//4gL5/+IC+//iAv3/4gOk/+IDqv/iA8b/4gQM/+IEEP/iBEv/4gRN/+IET//iBFH/4gRT/+IEVf/iBFf/4gRf/+IEoP/iAAYAEP+EABL/hAGG/4QBiv+EAY7/hAGP/4QAEQAu/+wAOf/sApX/7AKW/+wCl//sApj/7ALl/+wDFP/sAxb/7AMY/+wDGv/sAxz/7AMe/+wDsv/sBGL/7ARk/+wEwf/sACAABv/yAAv/8gBa//MAXf/zAL3/8wD2//UBGv/zAYT/8gGF//IBh//yAYj/8gGJ//ICtP/zArX/8wMj//MDpv/zA8n/8wPS//MD2v/zA9v/8gPc//ID3//yA+v/8wPz//MEFP/zBBb/8wQY//MEcf/zBHP/8wR1//MEw//zBMX/8wA/ACf/8wAr//MAM//zADX/8wCD//MAk//zAJj/8wCz//MAxAANANP/8wEI//MBF//zARv/8wEd//MBH//zASH/8wFB//MBav/zAkX/8wJG//MCSP/zAkn/8wKG//MCkP/zApH/8wKS//MCk//zApT/8wK8//MCvv/zAsD/8wLC//MC0P/zAtL/8wLU//MC1v/zAvj/8wL6//MC/P/zAy3/8wOK//MDl//zA73/8wPA//MD7f/zA/D/8wQL//MEDf/zBA//8wRK//METP/zBE7/8wRQ//MEUv/zBFT/8wRW//MEWP/zBFr/8wRc//MEXv/zBGD/8wSf//MEuP/zAEAAJ//mACv/5gAz/+YANf/mAIP/5gCT/+YAmP/mALP/5gC4/8IAxAAQANP/5gEI/+YBF//mARv/5gEd/+YBH//mASH/5gFB/+YBav/mAkX/5gJG/+YCSP/mAkn/5gKG/+YCkP/mApH/5gKS/+YCk//mApT/5gK8/+YCvv/mAsD/5gLC/+YC0P/mAtL/5gLU/+YC1v/mAvj/5gL6/+YC/P/mAy3/5gOK/+YDl//mA73/5gPA/+YD7f/mA/D/5gQL/+YEDf/mBA//5gRK/+YETP/mBE7/5gRQ/+YEUv/mBFT/5gRW/+YEWP/mBFr/5gRc/+YEXv/mBGD/5gSf/+YEuP/mADgAJf/kADz/0gA9/9MAsv/kALT/5ADE/+IA2v/SAQ3/5AEz/9IBQ//SAV3/0gJ//+QCgP/kAoH/5AKC/+QCg//kAoT/5AKF/+QCmf/TArb/5AK4/+QCuv/kAyL/0wMk/9MDhv/kA4v/0wOO/+QDmv/TA5v/0gOd/9MDtv/kA8L/0gPZ/9MD4v/kA/L/0wP1/9ID+P/kA/r/5AQD/9IEHv/kBCD/5AQi/+QEJP/kBCb/5AQo/+QEKv/kBCz/5AQu/+QEMP/kBDL/5AQ0/+QEcP/TBHL/0wR0/9MEhv/TBJn/5AAoABD/RgAS/0YAJf/NALL/zQC0/80Ax//yAQ3/zQGG/0YBiv9GAY7/RgGP/0YCf//NAoD/zQKB/80Cgv/NAoP/zQKE/80Chf/NArb/zQK4/80Cuv/NA4b/zQOO/80Dtv/NA+L/zQP4/80D+v/NBB7/zQQg/80EIv/NBCT/zQQm/80EKP/NBCr/zQQs/80ELv/NBDD/zQQy/80ENP/NBJn/zQABAMQADgC5AEf/3ABI/9wASf/cAEv/3ABR/8EAUv/BAFP/1gBU/8EAVf/cAFn/3QBa/+EAXf/hAJT/3ACZ/9wAm//dALv/3AC9/+EAv//mAMH/wQDC/+sAw//pAMX/8ADG/+cAyP/cAMn/3ADK/+MAy//dAMz/zgDN/9QAzv/bAOz/wQDw/8EA8f/BAPP/wQD0/8EA9f/BAPf/3AD4/8EA+v/BAPv/wQD+/8EBAP/BAQP/3AEF/8EBGP/WARr/4QEe/9wBIv/cASv/wQE2/8EBPP/BAT7/wQFC/9wBU//BAVX/wQFX/8EBXP/BAWD/3AFh/9wBa//cAqH/3AKi/9wCo//cAqT/3AKl/9wCqv/BAqv/1gKs/9YCrf/WAq7/1gKv/9YCsP/dArH/3QKy/90Cs//dArT/4QK1/+ECvf/cAr//3ALB/9wCw//cAsX/3ALH/9wCyf/cAsv/3ALN/9wCz//cAtH/3ALT/9wC1f/cAtf/3ALy/8EC9P/BAvb/wQL3/8EC+f/WAvv/1gL9/9YDFf/dAxf/3QMZ/90DG//dAx3/3QMf/90DI//hA57/3AOg/8EDov/dA6T/1gOm/+EDqf/dA6r/1gOr/90DxP/cA8X/wQPG/9YDx//BA8j/3APJ/+EDy//cA8z/wQPR/8ED0v/hA9r/4QPh/8ED5v/cA+f/wQPr/+ED7P/cA/H/3APz/+ED///cBAH/3AQC/9wECP/BBAr/wQQM/9YEDv/cBBD/1gQU/+EEFv/hBBj/4QQc/8EEHf/cBDf/3AQ5/9wEO//cBD3/3AQ//9wEQf/cBEP/3ARF/9wES//WBE3/1gRP/9YEUf/WBFP/1gRV/9YEV//WBFn/3ARb/9wEXf/cBF//1gRh/9wEY//dBGX/3QRn/90Eaf/dBGv/3QRt/90Eb//dBHH/4QRz/+EEdf/hBHz/wQSY/8EEnP/cBKD/1gSk/90Eqf/cBKv/3AS1/8EEt//BBMP/4QTF/+EAfAAG/9oAC//aAEf/8ABI//AASf/wAEv/8ABV//AAWf/vAFr/3ABd/9wAlP/wAJn/8ACb/+8Au//wAL3/3ADC/+wAxAAPAMb/6gDI//AAyf/wAMr/zgDL/+8AzP/nAPf/8AED//ABGv/cAR7/8AEi//ABQv/wAWD/8AFh//ABa//wAYT/2gGF/9oBh//aAYj/2gGJ/9oCof/wAqL/8AKj//ACpP/wAqX/8AKw/+8Csf/vArL/7wKz/+8CtP/cArX/3AK9//ACv//wAsH/8ALD//ACxf/wAsf/8ALJ//ACy//wAs3/8ALP//AC0f/wAtP/8ALV//AC1//wAxX/7wMX/+8DGf/vAxv/7wMd/+8DH//vAyP/3AOe//ADov/vA6b/3AOp/+8Dq//vA8T/8API//ADyf/cA8v/8APS/9wD2v/cA9v/2gPc/9oD3//aA+b/8APr/9wD7P/wA/H/8APz/9wD///wBAH/8AQC//AEDv/wBBT/3AQW/9wEGP/cBB3/8AQ3//AEOf/wBDv/8AQ9//AEP//wBEH/8ARD//AERf/wBFn/8ARb//AEXf/wBGH/8ARj/+8EZf/vBGf/7wRp/+8Ea//vBG3/7wRv/+8Ecf/cBHP/3AR1/9wEnP/wBKT/7wSp//AEq//wBMP/3ATF/9wARwAQAAwAEgAMAEf/5wBI/+cASf/nAEv/5wBV/+cAlP/nAJn/5wC7/+cAxAAPAMj/5wDJ/+cA9//nAQP/5wEe/+cBIv/nAUL/5wFg/+cBYf/nAWv/5wGGAAwBigAMAY4ADAGPAAwCof/nAqL/5wKj/+cCpP/nAqX/5wK9/+cCv//nAsH/5wLD/+cCxf/nAsf/5wLJ/+cCy//nAs3/5wLP/+cC0f/nAtP/5wLV/+cC1//nA57/5wPE/+cDyP/nA8v/5wPm/+cD7P/nA/H/5wP//+cEAf/nBAL/5wQO/+cEHf/nBDf/5wQ5/+cEO//nBD3/5wQ//+cEQf/nBEP/5wRF/+cEWf/nBFv/5wRd/+cEYf/nBJz/5wSp/+cEq//nAAYAyv/qAO3/7gD2/9UA/v/tATr/7AFt/+wAAQD2/8AAAQDKACAAvgAGAAwACwAMAEf/6ABI/+gASf/oAEoADABL/+gAU//qAFX/6ABaAAsAXQALAJT/6ACZ/+gAu//oAL0ACwDE/5AAxgALAMj/6ADJ/+gAygAMAPf/6AED/+gBGP/qARoACwEe/+gBIv/oAUL/6AFg/+gBYf/oAWv/6AGEAAwBhQAMAYcADAGIAAwBiQAMAdMADQHWAA0B2AAOAdn/9QHb/+wB3f/tAeX/7AHr/78B7P/tAe3/vwH0AA4B9f/tAfgADgIQAA4CEf/tAhIADQIUAA4CGv/tAjH/7gIz/78Cof/oAqL/6AKj/+gCpP/oAqX/6AKr/+oCrP/qAq3/6gKu/+oCr//qArQACwK1AAsCvf/oAr//6ALB/+gCw//oAsX/6ALH/+gCyf/oAsv/6ALN/+gCz//oAtH/6ALT/+gC1f/oAtf/6AL5/+oC+//qAv3/6gMjAAsDMv+/AzP/vwM0/78DNf+/Azb/vwM3/78DOP+/Azn/7QND/+0DRP/tA0X/7QNG/+0DR//tA0wADQNN/78DTv+/A0//vwNQ/+0DUf/tA1L/7QNT/+0DWv/tA1v/7QNc/+0DXf/tA23/7QNu/+0Db//tA3P/9QN0//UDdf/1A3b/9QN4AA4DgQANA4IADQOe/+gDpP/qA6YACwOq/+oDxP/oA8b/6gPI/+gDyQALA8v/6APSAAsD2gALA9sADAPcAAwD3wAMA+b/6APrAAsD7P/oA/H/6APzAAsD///oBAH/6AQC/+gEDP/qBA7/6AQQ/+oEFAALBBYACwQYAAsEHf/oBDf/6AQ5/+gEO//oBD3/6AQ//+gEQf/oBEP/6ARF/+gES//qBE3/6gRP/+oEUf/qBFP/6gRV/+oEV//qBFn/6ARb/+gEXf/oBF//6gRh/+gEcQALBHMACwR1AAsEnP/oBKD/6gSp/+gEq//oBMMACwTFAAsEy/+/BM//7QTQAA0E0v+/BN4ADQThAA0E6v+/BPH/7QT0/+0E9QAOBPn/7QT6AA0AAQD2/+IADgBc/+0AXv/tAO7/7QD2/8ABNP/tAUT/7QFe/+0DJv/tAyj/7QMq/+0Dyv/tA/b/7QQE/+0Eyf/tAA0AXP/yAF7/8gDu//IBNP/yAUT/8gFe//IDJv/yAyj/8gMq//IDyv/yA/b/8gQE//IEyf/yACIAWv/0AFz/8gBd//QAXv/zAL3/9ADu//IBGv/0ATT/8gFE//IBXv/yArT/9AK1//QDI//0Ayb/8wMo//MDKv/zA6b/9APJ//QDyv/yA9L/9APa//QD6//0A/P/9AP2//IEBP/yBBT/9AQW//QEGP/0BHH/9ARz//QEdf/0BMP/9ATF//QEyf/zAGIABv/KAAv/ygA4/9IAOv/UADz/9AA9/9MAWv/mAFz/7wBd/+YAvf/mANL/0gDW/9IA2v/0AN7/7QDh/+EA5v/UAO7/7wD2/8kA/v/RAQn/5QEZ/9QBGv/mASD/4wEz//QBNP/vATn/0gE6/8QBQ//0AUT/7wFF/9IBR//hAUn/4QFd//QBXv/vAWL/1AFj//UBZP/nAWz/ZAFt/8kBhP/KAYX/ygGH/8oBiP/KAYn/ygKZ/9MCtP/mArX/5gMO/9IDEP/SAxL/0gMi/9MDI//mAyT/0wOL/9MDmv/TA5v/9AOd/9MDpv/mA7X/7QPB/9IDwv/0A8n/5gPK/+8D0v/mA9n/0wPa/+YD2//KA9z/ygPf/8oD6v/UA+v/5gPy/9MD8//mA/X/9AP2/+8EA//0BAT/7wQT/+0EFP/mBBX/7QQW/+YEF//tBBj/5gQZ/+EEcP/TBHH/5gRy/9MEc//mBHT/0wR1/+YEd//SBHn/4QSG/9MEv//SBML/1ATD/+YExP/UBMX/5gByAAb/wAAL/8AAOP+dADr/xwA8//AAPf+rAFH/0gBS/9IAVP/SAMH/0gDS/50A1P/1ANb/nQDa//AA3f/1AN7/6gDh/+UA5v/BAOz/0gDw/9IA8f/SAPP/0gD0/9IA9f/SAPb/zQD4/9IA+v/SAPv/0gD+/9IBAP/SAQX/0gEZ/8cBK//SATP/8AE2/9IBOf+dATr/zAE8/9IBPv/SAUP/8AFF/50BR//lAUn/5QFM/98BUP/1AVP/0gFV/9IBV//SAVz/0gFd//ABYv/OAWT/6gFm//UBbP+eAW3/zgFv//UBhP/AAYX/wAGH/8ABiP/AAYn/wAKZ/6sCqv/SAvL/0gL0/9IC9v/SAvf/0gMO/50DEP+dAxL/nQMi/6sDJP+rA4v/qwOa/6sDm//wA53/qwOg/9IDtf/qA8H/nQPC//ADxf/SA8f/0gPM/9ID0f/SA9n/qwPb/8AD3P/AA9//wAPh/9ID5//SA+r/xwPy/6sD9f/wBAP/8AQI/9IECv/SBBP/6gQV/+oEF//qBBn/5QQc/9IEcP+rBHL/qwR0/6sEd/+dBHn/5QR8/9IEhv+rBJj/0gS1/9IEt//SBL//nQTC/8cExP/HAHUABv+xAAv/sQA4/54AOv/FADz/8gA9/6gAUf/PAFL/zwBU/88AXP/vAMH/zwDS/54A1v+eANr/8gDe/+wA4f/hAOb/wgDs/88A7v/vAPD/zwDx/88A8//PAPT/zwD1/88A9v/GAPj/zwD6/88A+//PAP7/zwEA/88BBf/PARn/xQEr/88BM//yATT/7wE2/88BOf+eATr/wAE8/88BPv/PAUP/8gFE/+8BRf+eAUf/4QFJ/+EBTP/fAVP/zwFV/88BV//PAVz/zwFd//IBXv/vAWL/zQFk/+gBbP+fAW3/xgGE/7EBhf+xAYf/sQGI/7EBif+xApn/qAKq/88C8v/PAvT/zwL2/88C9//PAw7/ngMQ/54DEv+eAyL/qAMk/6gDi/+oA5r/qAOb//IDnf+oA6D/zwO1/+wDwf+eA8L/8gPF/88Dx//PA8r/7wPM/88D0f/PA9n/qAPb/7ED3P+xA9//sQPh/88D5//PA+r/xQPy/6gD9f/yA/b/7wQD//IEBP/vBAj/zwQK/88EE//sBBX/7AQX/+wEGf/hBBz/zwRw/6gEcv+oBHT/qAR3/54Eef/hBHz/zwSG/6gEmP/PBLX/zwS3/88Ev/+eBML/xQTE/8UAUwA4/74AUf/hAFL/4QBU/+EAWv/vAF3/7wC9/+8Awf/hANL/vgDW/74A5v/JAOz/4QDw/+EA8f/hAPP/4QD0/+EA9f/hAPb/3wD4/+EA+v/hAPv/4QD+/+EBAP/hAQX/4QEJ/+0BGv/vASD/6wEr/+EBNv/hATn/vgE6/98BPP/hAT7/4QFF/74BTP/pAVP/4QFV/+EBV//hAVz/4QFj//UBbf/gAqr/4QK0/+8Ctf/vAvL/4QL0/+EC9v/hAvf/4QMO/74DEP++AxL/vgMj/+8DoP/hA6b/7wPB/74Dxf/hA8f/4QPJ/+8DzP/hA9H/4QPS/+8D2v/vA+H/4QPn/+ED6//vA/P/7wQI/+EECv/hBBT/7wQW/+8EGP/vBBz/4QRx/+8Ec//vBHX/7wR3/74EfP/hBJj/4QS1/+EEt//hBL//vgTD/+8Exf/vAGoAOP/mADr/5wA8//IAPf/nAFH/1gBS/9YAVP/WAFz/8QDB/9YA0v/mANb/5gDa//IA3v/uAOH/6ADm/+YA7P/WAO7/8QDw/9YA8f/WAPP/1gD0/9YA9f/WAPb/0AD4/9YA+v/WAPv/1gD+/9YBAP/WAQX/1gEZ/+cBK//WATP/8gE0//EBNv/WATn/5gE6/84BPP/WAT7/1gFD//IBRP/xAUX/5gFH/+gBSf/oAVP/1gFV/9YBV//WAVz/1gFd//IBXv/xAWL/5wFk/+0BbP/mAW3/0AKZ/+cCqv/WAvL/1gL0/9YC9v/WAvf/1gMO/+YDEP/mAxL/5gMi/+cDJP/nA4v/5wOa/+cDm//yA53/5wOg/9YDtf/uA8H/5gPC//IDxf/WA8f/1gPK//EDzP/WA9H/1gPZ/+cD4f/WA+f/1gPq/+cD8v/nA/X/8gP2//EEA//yBAT/8QQI/9YECv/WBBP/7gQV/+4EF//uBBn/6AQc/9YEcP/nBHL/5wR0/+cEd//mBHn/6AR8/9YEhv/nBJj/1gS1/9YEt//WBL//5gTC/+cExP/nAJgAJQAQACf/6AAr/+gAM//oADX/6AA4/+AAOv/gAD3/3wCD/+gAk//oAJj/6ACyABAAs//oALQAEADS/+AA0//oANQAEADW/+AA2QAUAN0AEADh/+EA5v/gAO0AEwDyABAA+f/gAQQAEAEI/+gBDQAQARf/6AEZ/+ABG//oAR3/6AEf/+gBIf/oATn/4AFB/+gBRf/gAUf/4QFI/+ABSf/hAUr/4AFN/+EBUAAQAVEAEAFY/+kBYv/fAWT/3gFmABABav/oAWz/3wFu//IBbwAQAXAAEAJF/+gCRv/oAkj/6AJJ/+gCfwAQAoAAEAKBABACggAQAoMAEAKEABAChQAQAob/6AKQ/+gCkf/oApL/6AKT/+gClP/oApn/3wK2ABACuAAQAroAEAK8/+gCvv/oAsD/6ALC/+gC0P/oAtL/6ALU/+gC1v/oAvj/6AL6/+gC/P/oAw7/4AMQ/+ADEv/gAyL/3wMk/98DLf/oA4YAEAOK/+gDi//fA44AEAOX/+gDmv/fA53/3wO2ABADvf/oA8D/6APB/+AD2f/fA+IAEAPq/+AD7f/oA/D/6APy/98D+AAQA/oAEAQL/+gEDf/oBA//6AQZ/+EEGv/gBB4AEAQgABAEIgAQBCQAEAQmABAEKAAQBCoAEAQsABAELgAQBDAAEAQyABAENAAQBEr/6ARM/+gETv/oBFD/6ARS/+gEVP/oBFb/6ARY/+gEWv/oBFz/6ARe/+gEYP/oBHD/3wRy/98EdP/fBHf/4AR5/+EEev/gBIb/3wSZABAEn//oBLj/6AS//+AEwv/gBMT/4AA1ABv/8gA4//EAOv/0ADz/9AA9//AA0v/xANT/9QDW//EA2v/0AN3/9QDe//MA5v/xARn/9AEz//QBOf/xAUP/9AFF//EBUP/1AV3/9AFi//IBZP/yAWb/9QFs//IBb//1Apn/8AMO//EDEP/xAxL/8QMi//ADJP/wA4v/8AOa//ADm//0A53/8AO1//MDwf/xA8L/9APZ//AD6v/0A/L/8AP1//QEA//0BBP/8wQV//MEF//zBHD/8ARy//AEdP/wBHf/8QSG//AEv//xBML/9ATE//QAawAlAA8AOP/mADr/5gA8AA4APf/mALIADwC0AA8A0v/mANQADgDW/+YA2QATANoADgDdAA4A3gALAOH/5QDm/+YA5//0AO0AEgDyAA8A9v/nAPn/6AD+/+cBBAAPAQ0ADwEZ/+YBMwAOATn/5gE6/+cBQwAOAUX/5gFH/+UBSP/oAUn/5QFK/+gBTP/kAVAADgFRAA8BXQAOAWL/5gFk/+YBZgAOAWz/5gFt/+cBbwAOAXAADwJ/AA8CgAAPAoEADwKCAA8CgwAPAoQADwKFAA8Cmf/mArYADwK4AA8CugAPAw7/5gMQ/+YDEv/mAyL/5gMk/+YDhgAPA4v/5gOOAA8Dmv/mA5sADgOd/+YDtQALA7YADwPB/+YDwgAOA9n/5gPiAA8D6v/mA/L/5gP1AA4D+AAPA/oADwQDAA4EEwALBBUACwQXAAsEGf/lBBr/6AQeAA8EIAAPBCIADwQkAA8EJgAPBCgADwQqAA8ELAAPBC4ADwQwAA8EMgAPBDQADwRw/+YEcv/mBHT/5gR3/+YEef/lBHr/6ASG/+YEmQAPBL//5gTC/+YExP/mADoABv+/AAv/vwA4/58AOv/JAD3/rQDS/58A1v+fAN7/7ADh/+YA5v/EAPb/zQD+/9UBGf/JATn/nwE6/8wBRf+fAUf/5gFJ/+YBTP/fAWL/0QFk/+wBbP+hAW3/zwGE/78Bhf+/AYf/vwGI/78Bif+/Apn/rQMO/58DEP+fAxL/nwMi/60DJP+tA4v/rQOa/60Dnf+tA7X/7APB/58D2f+tA9v/vwPc/78D3/+/A+r/yQPy/60EE//sBBX/7AQX/+wEGf/mBHD/rQRy/60EdP+tBHf/nwR5/+YEhv+tBL//nwTC/8kExP/JADEAOP/jADz/5QA9/+QA0v/jANT/5QDW/+MA2f/iANr/5QDd/+UA3v/pAPL/6gEE/+oBM//lATn/4wFD/+UBRf/jAVD/5QFR/+oBXf/lAWb/5QFs/+QBb//lAXD/6gKZ/+QDDv/jAxD/4wMS/+MDIv/kAyT/5AOL/+QDmv/kA5v/5QOd/+QDtf/pA8H/4wPC/+UD2f/kA/L/5AP1/+UEA//lBBP/6QQV/+kEF//pBHD/5ARy/+QEdP/kBHf/4wSG/+QEv//jACQAOP/iADz/5ADS/+IA1P/kANb/4gDZ/+EA2v/kAN3/5ADe/+kA7f/kAPL/6wEE/+sBM//kATn/4gFD/+QBRf/iAVD/5AFR/+sBXf/kAWb/5AFv/+QBcP/rAw7/4gMQ/+IDEv/iA5v/5AO1/+kDwf/iA8L/5AP1/+QEA//kBBP/6QQV/+kEF//pBHf/4gS//+IAGAA4/+sAPf/zANL/6wDW/+sBOf/rAUX/6wKZ//MDDv/rAxD/6wMS/+sDIv/zAyT/8wOL//MDmv/zA53/8wPB/+sD2f/zA/L/8wRw//MEcv/zBHT/8wR3/+sEhv/zBL//6wA5AFH/7wBS/+8AVP/vAFz/8ADB/+8A7P/vAO3/7gDu//AA8P/vAPH/7wDz/+8A9P/vAPX/7wD2/+4A+P/vAPr/7wD7/+8A/v/vAQD/7wEF/+8BCf/0ASD/8QEr/+8BNP/wATb/7wE6/+8BPP/vAT7/7wFE//ABU//vAVX/7wFX/+8BXP/vAV7/8AFt/+8Cqv/vAvL/7wL0/+8C9v/vAvf/7wOg/+8Dxf/vA8f/7wPK//ADzP/vA9H/7wPh/+8D5//vA/b/8AQE//AECP/vBAr/7wQc/+8EfP/vBJj/7wS1/+8Et//vACQABv/yAAv/8gBa//UAXf/1AL3/9QD2//QA/v/0AQn/9QEa//UBOv/1AW3/9QGE//IBhf/yAYf/8gGI//IBif/yArT/9QK1//UDI//1A6b/9QPJ//UD0v/1A9r/9QPb//ID3P/yA9//8gPr//UD8//1BBT/9QQW//UEGP/1BHH/9QRz//UEdf/1BMP/9QTF//UANQBR/+4AUv/uAFT/7gDB/+4A7P/uAO0AFADw/+4A8f/uAPP/7gD0/+4A9f/uAPb/7QD4/+4A+f/tAPr/7gD7/+4A/P/QAP7/7gEA/+4BBf/uASv/7gE2/+4BOv/tATz/7gE+/+4BSP/tAUr/7QFT/+4BVf/uAVf/7gFc/+4Bbf/tAqr/7gLy/+4C9P/uAvb/7gL3/+4DoP/uA8X/7gPH/+4DzP/uA9H/7gPh/+4D5//uBAj/7gQK/+4EGv/tBBz/7gR6/+0EfP/uBJj/7gS1/+4Et//uAAoABv/1AAv/9QGE//UBhf/1AYf/9QGI//UBif/1A9v/9QPc//UD3//1AHYAR//wAEj/8ABJ//AAS//wAFP/xwBV//AAlP/wAJn/8AC7//AAyP/wAMn/8AD3//ABA//wARj/xwEc/+sBHv/wASL/8AFC//ABYP/wAWH/8AFr//AB2//rAd3/6wHl/+kB7P/rAfX/6wIR/+sCGv/rAjH/6wKh//ACov/wAqP/8AKk//ACpf/wAqv/xwKs/8cCrf/HAq7/xwKv/8cCvf/wAr//8ALB//ACw//wAsX/8ALH//ACyf/wAsv/8ALN//ACz//wAtH/8ALT//AC1f/wAtf/8AL5/8cC+//HAv3/xwM5/+sDQ//rA0T/6wNF/+sDRv/rA0f/6wNQ/+sDUf/rA1L/6wNT/+sDWv/rA1v/6wNc/+sDXf/rA23/6wNu/+sDb//rA57/8AOk/8cDqv/HA8T/8APG/8cDyP/wA8v/8APm//AD7P/wA/H/8AP///AEAf/wBAL/8AQM/8cEDv/wBBD/xwQd//AEN//wBDn/8AQ7//AEPf/wBD//8ARB//AEQ//wBEX/8ARL/8cETf/HBE//xwRR/8cEU//HBFX/xwRX/8cEWf/wBFv/8ARd//AEX//HBGH/8ASc//AEoP/HBKn/8ASr//AEz//rBPH/6wT0/+sE+f/rAOIABgANAAsADQBF//AAR//AAEj/wABJ/8AASgANAEv/wABT/+IAVf/AAFoACwBdAAsAlP/AAJn/wAC7/8AAvQALAMf/1gDI/8AAyf/AAMz/1QDt/8gA8v/XAPf/wAED/8ABBP/XARj/4gEaAAsBHP/sAR7/wAEgAAwBIv/AAUL/wAFR/9cBYP/AAWH/wAFjAAsBZQALAWv/wAFw/9cBhAANAYUADQGHAA0BiAANAYkADQHTAA0B1gANAdgADgHZ//UB2//sAd3/7QHl/+wB6/+/Aez/7QHt/78B9AAOAfX/7QH4AA4CEAAOAhH/7QISAA0CFAAOAhr/7QIx/+4CM/+/Apr/8AKb//ACnP/wAp3/8AKe//ACn//wAqD/8AKh/8ACov/AAqP/wAKk/8ACpf/AAqv/4gKs/+ICrf/iAq7/4gKv/+ICtAALArUACwK3//ACuf/wArv/8AK9/8ACv//AAsH/wALD/8ACxf/AAsf/wALJ/8ACy//AAs3/wALP/8AC0f/AAtP/wALV/8AC1//AAvn/4gL7/+IC/f/iAyMACwMy/78DM/+/AzT/vwM1/78DNv+/Azf/vwM4/78DOf/tA0P/7QNE/+0DRf/tA0b/7QNH/+0DTAANA03/vwNO/78DT/+/A1D/7QNR/+0DUv/tA1P/7QNa/+0DW//tA1z/7QNd/+0Dbf/tA27/7QNv/+0Dc//1A3T/9QN1//UDdv/1A3gADgOBAA0DggANA57/wAOk/+IDpgALA6r/4gPD//ADxP/AA8b/4gPI/8ADyQALA8v/wAPSAAsD2gALA9sADQPcAA0D3wANA+P/8APm/8AD6wALA+z/wAPx/8AD8wALA/n/8AP7//AD///ABAH/wAQC/8AEDP/iBA7/wAQQ/+IEFAALBBYACwQYAAsEHf/ABB//8AQh//AEI//wBCX/8AQn//AEKf/wBCv/8AQt//AEL//wBDH/8AQz//AENf/wBDf/wAQ5/8AEO//ABD3/wAQ//8AEQf/ABEP/wARF/8AES//iBE3/4gRP/+IEUf/iBFP/4gRV/+IEV//iBFn/wARb/8AEXf/ABF//4gRh/8AEcQALBHMACwR1AAsEmv/wBJz/wASg/+IEqf/ABKv/wATDAAsExQALBMv/vwTP/+0E0AANBNL/vwTeAA0E4QANBOr/vwTx/+0E9P/tBPUADgT5/+0E+gANAA8A7QAUAPIAEAD2//AA+f/wAP7/8AEBABYBBAAQATr/5gFI//ABSv/cAVEAEAFt//ABcAAQBBr/8AR6//AATwBH/+4ASP/uAEn/7gBL/+4AVf/uAJT/7gCZ/+4Au//uAMj/7gDJ/+4A7QASAPIADgD2/+MA9//uAPn/4wD8/7gA/v/jAQP/7gEEAA4BHv/uASL/7gE6/7oBQv/uAUj/4wFK/9kBUQAOAWD/7gFh/+4Ba//uAW3/4wFwAA4Cof/uAqL/7gKj/+4CpP/uAqX/7gK9/+4Cv//uAsH/7gLD/+4Cxf/uAsf/7gLJ/+4Cy//uAs3/7gLP/+4C0f/uAtP/7gLV/+4C1//uA57/7gPE/+4DyP/uA8v/7gPm/+4D7P/uA/H/7gP//+4EAf/uBAL/7gQO/+4EGv/jBB3/7gQ3/+4EOf/uBDv/7gQ9/+4EP//uBEH/7gRD/+4ERf/uBFn/7gRb/+4EXf/uBGH/7gR6/+MEnP/uBKn/7gSr/+4AIgBa/8AAXf/AAL3/wAD2/4AA+f/uAP7/8AEJ/9sBGv/AASD/3AE6/0cBSP/uAUr/7gFjAAcBZf/0AW3/fwK0/8ACtf/AAyP/wAOm/8ADyf/AA9L/wAPa/8AD6//AA/P/wAQU/8AEFv/ABBj/wAQa/+4Ecf/ABHP/wAR1/8AEev/uBMP/wATF/8AAIwBa//QAXP/wAF3/9AC9//QA7f/vAO7/8ADy//MA/v/uAQT/8wEa//QBNP/wAUT/8AFR//MBXv/wAXD/8wK0//QCtf/0AyP/9AOm//QDyf/0A8r/8APS//QD2v/0A+v/9APz//QD9v/wBAT/8AQU//QEFv/0BBj/9ARx//QEc//0BHX/9ATD//QExf/0AAoABv/WAAv/1gGE/9YBhf/WAYf/1gGI/9YBif/WA9v/1gPc/9YD3//WABUAXP/gAO7/4AD2/3YA+f/CAP7/0wEJ/9kBIP/bATT/4AE6/x4BRP/gAUj/wgFK/+0BXv/gAWP/8AFl//IBbf9WA8r/4AP2/+AEBP/gBBr/wgR6/8IADQD2/2QA+f/SAP7/2QEJ/9kBIP/bATr/HgFI/9IBSv/tAWP/8AFl//IBbf9WBBr/0gR6/9IACQD2/2oA/v/GAQn/2QEg/9sBOv8eAUr/7QFj//ABZf/yAW3/VgAKAAb/1wAL/9cBhP/XAYX/1wGH/9cBiP/XAYn/1wPb/9cD3P/XA9//1wBiAEf/mABI/5gASf+YAEv/mABT/3AAVf+YAFf/GABbAAsAlP+YAJn/mAC7/5gAyP+YAMn/mAD3/5gBA/+YARj/cAEe/5gBIv+YAUL/mAFg/5gBYf+YAWv/mAHB/xgCof+YAqL/mAKj/5gCpP+YAqX/mAKr/3ACrP9wAq3/cAKu/3ACr/9wAr3/mAK//5gCwf+YAsP/mALF/5gCx/+YAsn/mALL/5gCzf+YAs//mALR/5gC0/+YAtX/mALX/5gC+f9wAvv/cAL9/3ADBf8YAwf/GAMJ/xgDC/8YAw3/GAOe/5gDpP9wA6r/cAPE/5gDxv9wA8j/mAPL/5gDzf8YA+b/mAPs/5gD8f+YA///mAQB/5gEAv+YBAz/cAQO/5gEEP9wBB3/mAQ3/5gEOf+YBDv/mAQ9/5gEP/+YBEH/mARD/5gERf+YBEv/cARN/3AET/9wBFH/cART/3AEVf9wBFf/cARZ/5gEW/+YBF3/mARf/3AEYf+YBJz/mASg/3AEqf+YBKv/mAS+/xgAEwHT/+4B1f/1Adb/8QHY//IB9P/yAfj/8gIQ//ICEv/uAhT/8gNM/+4DeP/yA4D/9QOB/+4Dgv/uBND/7gTe/+4E4f/uBPX/8gT6/+4AEwHT/+UB1f/xAdb/6wHY/+kB9P/pAfj/6QIQ/+kCEv/lAhT/6QNM/+UDeP/pA4D/8QOB/+UDgv/lBND/5QTe/+UE4f/lBPX/6QT6/+UAAwHV//UB1v/uA4D/9QACAdb/twHb//AAAQBbAAsABAAN/+YAQf/0AGH/7wFN/+0AFgC4/9QAwv/tAMQAEQDK/+AAzP/nAM3/5QDO/+4A2QASAOr/6QD2/9cBOv/XAUr/0wFM/9YBTf/FAVj/5wFiAA0BZAAMAW3/1gFu//IB2//pAeX/5wIx/+kAAQEc//EAEgDZ/64A5gASAOv/4ADt/60A7//WAP3/3wEB/9IBB//gARz/zgEu/90BMP/iATj/4AFA/+ABSv/pAU3/2gFf/70Baf/fAWwAEQACAPb/9QGF/8AAAgDt/2gBHP/uAAoA5v/DAPb/zwD+/9QBOv/OAUn/5wFM/98BYv/RAWT/7AFs/6ABbf/RADAAVv9+AFv/nQBt/vEAfP70AIH+qwCG/14Aif9LALj/cgC//w8Aw/8KAMb/QQDH/wcAyv9oAMz/DwDN/w4Azv8MANn/YwDmAAUA6v+9AOv/SQDt/v4A7/8TAPb/aAD9/w4A/v9oAP//EwEB/wcBAgAwAQf/DgEJ/xEBHP7nASD/rAEu/xUBMP88ATj/DgE6/2oBQP9JAUr/DAFM/z8BTf7xAVj/wAFf/u8BY/8xAWX/XwFp/woBbAAFAW3/MAFu/9UAHQAK/+IADQAUAA7/zwBBABIASv/qAFb/2ABY/+oAYQATAG3/rgB8/80Agf+gAIb/wQCJ/8AAuP/QALz/6gC//8YAwAANAML/6QDD/9YAxv/oAMf/ugDK/+kAzP/LAM3/2gDO/8cBjf/TAdv/ywHl/8sCMf/NABgAI/+vAFj/7wBb/98Amv/uALj/5QC5/9EAxAARAMr/yADZABMA5v/FAPb/ygD+/9ABOv+BAUn/ZQFK/4UBTP9mAU3/3QFY//IBYv+xAWT/ygFs/6kBbf/IAdb/zQHl//UACAD2//AA/v/wAQn/8QEg//MBOv/xAWP/8wFl//MBbf/xAAMASv/uAFv/6gHW//AACQDK/+oA7f+4APb/4gEJ//ABIP/xATr/6wFj//UBbf/sAYX/kAACAREACwFs/+YAEwBb/8EAuP/FAMr/tADq/9cA9v+5AP7/6QEJ/7IBHP/SASD/yAE6/6ABSv/FAVj/5AFj/8wBZf/MAW3/ywFu/+8B2//nAeX/5gIx/+gABQBb/8wB1v+4Adv/8gHl//ECMf/zAAgA2QAVAO0AFQFJ/+QBSv/lAUz/5AFi/+MBZP/iAWz/5AACAPb/1gGF/4gABwBYAA4Agf7XAMT/mADH/8cA2f8SAO3/UgFf/88ABgDK/+oA7f/uAPb/1gD+/+0BOv/sAW3/7AAEAEoAFABYADIAWwARAYUAEAA0AAT/xABW/78AW//RAG3/bAB8/24Agf9DAIb/rACJ/6EAuP+4AL//fgDD/3sAxv+bAMf/eQDK/7IAzP9+AM3/fQDO/3wA2f+vAOYADwDq/+QA6/+gAO3/dADv/4AA9v+yAP3/fQD+/7IA//+AAQH/eQECACgBB/99AQn/fwEc/2YBIP/aAS7/gQEw/5gBOP99ATr/swFA/6ABSv98AUz/mgFN/2wBWP/mAV//awFj/5IBZf+tAWn/ewFsAA8Bbf+RAW7/8gHb/7kB5f+5AjH/uQAHAA0AFABBABEAVv/iAGEAEwHb/9kB5f/ZAjH/2QAGAEoADQDGAAsAx//qAMoADADt/8gBHP/xAAcADQAPAEEADABW/+sAYQAOAdv/5wHl/+cCMf/pAAYAW//lALj/ywDN/+QB2//sAeX/6wIx/+0ABwCB/98Atf/zALf/8ADE/+oA2f/fAOb/4AFs/+AAAQHb/+sABAHW/8cB2//yAeX/8gIx//IAAQHW//EAAQHWAA0AAgsMAAQAAA6sF2gAJgAlAAAAAAAAAAAAAAAAABIAAAAAAAAAAP/j/+QAAAAAAAAAAAARAAAAAAAAAAAAAAAAAAAAEQAAABEAAAAAAAAAAP/k/+UAAAAAAAAAAAAAAAAAAAAAAAD/6wAAAAAAAAAA/6v/1f/tAAAAAAAA/+oAAP/pAAAAAAAAAAAAAP/h/4YAAP/1/+oAAAAAAAAAAAAAAAAAAAAAAAD/6//Q//T/9QAAAAD/9f/O/+//iP9qAAAAAAAMAAAAAP/xAAD/iAAA/9n/xP/HABEAAAASAAD/swAAAAD/yf/fAAAAAP/dAAAAAAAAAAAAAAAAAAAAAAAA//EAAAAAAAAAAAAA//AAAAAAAAAAAP+o/+sAAAAAAAAAAAAA//AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/7AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+0AAAAA/+3/7wAAAAAAAP/mAAAAFAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/tAAAAAAAAAAAAAAAAAAAAAAAA//EAAAAAAAAAAAAAAAAAAAAAAAAAAP/vAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/9QAAAAAAAAAAAAD/8QAAAAAAAAAA/+P/8QAAAAAAAAAAAAD/8gAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/zAAAAAAAAAAAAAAAAAAAAAAAAAAD/8gAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/8wAAAAD/8QAAAAD/8QAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAPAAAAAAAAAAAAAP9Z/9cAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/6gAAAAAAAAAAAAAAAP/rAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+b/4QAA/+X/6QAAAAD/5//YAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/1wAAP+jAAAAAAAAAAD/v//j/9j/v//Z/2r/wf/L/+z/oAARABL/q//G/+L/8AANAAAAAAAA/+kAEQAA//MAAP8ZAAD/7wASAAD/aAAAAAAAAP+g//MAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+r/7gAAAAAAAP/sAAAAAAAAAAAAAAAAAAAAAAAA/6f/5P+n/zD/v/+I/1j/uf+uAAAAEAAQ/6//tP/E//AAAAAAAAAAAP+zAA8AAP/x/8v+/v9+/+0AEP+8/vAAAP98AAD/KP/xAAAAAAAAAAAAAAAAAAAAAP/yAAAAAAAAAAAAAAAAAAAAAAAA/+wAAAAAAAAAAP+//8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/9gAAP/wAAAAAP/wAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+v/5gAA/+v/7QANAAD/7P/lAAAAAAAAAA0AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/5v/nAAD/6//rAAAAAP/n/+EAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEQAAABEAAAAOAAD/ZAAA/9EAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+MAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+wAAAAA/9gAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/7QAAAAD/3AAAAAD/4gAAABIAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAP9TAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/zAAAAAP/zAAD/Tv/1AAAADwAAAAAAAP+AAAAAAAAA/80AAP/cAAAAAAAAAAAAAP9v/mz/pwAAAAAAAAAAAAAAAAAA/0gAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/9QAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/AAAAAAP/yABMAAP/y/4X/6P8z/ukAEwAAAAAAAAAA/+4AAP7gAAD/o/+3/70AAAAAAAAAAP8yAAAAAAAAAAAAAAAA/9cAAP/FAAD/7P+lAAD/iP/OAAAAAAAAAAAAAAAA/6QAAAAAAAAAAAAA/9sAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+wAAAAA/+wAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/YAAAAAAAAAAAAAAAAAAAAAAAAAAD/4QAAAAD/4f/t/9X/3//nAAAAAAAOAAD/ywAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP9xAAAAAAAAAAD/xAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+X/yQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+gAAAAAAAAAAP/zAAAAAAAA/9T/8wAA/9L/5P+1/9L/2f/1AAAAAAAA/7QAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/KQAAAAAAAAAA/2MAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/rAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/tQAAAAAAAAAAAAAAAAAAAAAAAAAA/3n/6wAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/4wAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/n/+tAAAAAAAAAAAAAAAAAAD/wP/JAAAAAAAAAAAAAAAAAAD/yAAAAAD/5wAA/+sAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP7jAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP9V/73/Vf9m/37/M/9fAAD/YQAAAAcABwAA/2v/hv/RAAAAAAAAAAD/agAFAAAAAP+S/jb/DwAAAAcAAP4eAAD/DAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/vAAAAAAAAAAAAAAAAAAAAAAAA/+wAAAAAAAAAAP+0/7sAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/9UAAP+9/+n/rv+9AAD/pf+vAAAAAAAAABIAEgAA/9IAAAAAAAAAAAAAAAAAAAAAAAAAAP/K/nf/uwAAAAAAAP85AAD/6QAAAAAAAAACAJoABgAGAAAACwALAAEAEAAQAAIAEgASAAMAJQApAAQALAA0AAkAOAA+ABIARQBHABkASQBJABwATABMAB0AUQBUAB4AVgBWACIAWgBaACMAXABeACQAigCKACcAlgCWACgAsQC0ACkAvQC9AC0AwQDBAC4AxwDHAC8A1ADVADAA1wDXADIA2gDaADMA3ADeADQA4ADmADcA7ADsAD4A7gDuAD8A9wD3AEAA/AD8AEEA/gD/AEIBBAEFAEQBCgEKAEYBDQENAEcBGAEaAEgBLgEwAEsBMwE1AE4BNwE3AFEBOQE5AFIBOwE7AFMBQwFEAFQBVAFUAFYBVgFWAFcBWAFYAFgBXAFeAFkBhAGKAFwBjgGPAGMB2AHYAGUB3QHdAGYB4AHhAGcB6wHtAGkB/wH/AGwCDgIQAG0CMAIwAHACMwIzAHECRQJFAHICRwJIAHMCegJ7AHUCfQJ9AHcCfwKlAHgCqgKvAJ8CtALEAKUCxgLPALYC2ALaAMAC3ALcAMMC3gLeAMQC4ALgAMUC4gLiAMYC5QLlAMcC5wLnAMgC6QLpAMkC6wLrAMoC7QLtAMsC7wLvAMwC8QL9AM0C/wL/ANoDAQMBANsDAwMDANwDDgMOAN0DEAMQAN4DEgMSAN8DFAMUAOADFgMWAOEDGAMYAOIDGgMaAOMDHAMcAOQDHgMeAOUDIAMgAOYDIgMqAOcDLwM4APADQwNHAPoDTQNPAP8DVANUAQIDZQNpAQMDbQNvAQgDeAN4AQsDhgOLAQwDjgOdARIDoAOgASIDpAOkASMDpgOmASQDqgOqASUDrQOuASYDsAO5ASgDuwO9ATIDvwPEATUDxgPMATsD0gPTAUID1QPVAUQD1wPXAUUD2QPcAUYD3wPkAUoD5gPmAVAD6gPrAVED8AP7AVMD/gP/AV8EAQQEAWEECwQMAWUEEAQQAWcEEgQYAWgEHgRGAW8ESARIAZgESgRXAZkEXwRfAacEYgRiAagEZARkAakEcAR1AaoEdwR3AbAEewR8AbEEfwR/AbMEgQSCAbQEhASEAbYEhgSGAbcElwSbAbgEnQSdAb0EnwSgAb4EogSiAcAEpgSoAcEEqgSqAcQErASuAcUEsASwAcgEsgSyAckEtAS6AcoEvAS8AdEEvwS/AdIEwQTGAdMEyATLAdkEzwTPAd0E0gTSAd4E2ATYAd8E3QTdAeAE6AToAeEE6gTqAeIE8QTxAeME9QT1AeQAAgF0AAYABgAZAAsACwAZABAAEAAhABIAEgAhACUAJQACACYAJgAcACcAJwATACgAKAABACkAKQAFAC4ALgAKAC8ALwALADAAMAAYADMAMwABADQANAAWADgAOAAOADkAOQAKADoAOgAdADsAOwAbADwAPAASAD0APQAMAD4APgARAEUARQAGAEYARgAHAEcARwAXAEkASQAIAEwATAAEAFEAUgAEAFMAUwADAFQAVAAHAFYAVgAVAFoAWgAJAFwAXAAUAF0AXQAJAF4AXgAQAIoAigAHAJYAlgABALEAsQAiALIAsgACALMAswABALQAtAACAL0AvQAJAMEAwQAEAMcAxwAHANQA1QAgANoA2gASAN4A3gAlAOQA5AAgAOYA5gAgAOwA7AAaAO4A7gAUAPcA9wAHAPwA/AAfAP4A/gAfAP8A/wAHAQQBBQAfAQoBCgAfAQ0BDQACARgBGAADARkBGQAdARoBGgAJAS4BLgAHAS8BLwAiATABMAAaATMBMwASATQBNAAUATUBNQALATcBNwALATkBOQALAUMBQwASAUQBRAAUAVgBWAABAVwBXAAaAV0BXQASAV4BXgAUAYQBhQAZAYYBhgAhAYcBiQAZAYoBigAhAY4BjwAhAdgB2AAjAd0B3QANAeAB4AAkAeEB4QAeAesB6wAPAewB7AANAe0B7QAPAf8B/wAeAg4CEAAeAjACMAANAjMCMwAPAkUCRQATAkcCSAABAnoCewABAn0CfQAOAn8ChQACAoYChgATAocCigAFApAClAABApUCmAAKApkCmQAMApoCoAAGAqECoQAXAqICpQAIAqoCqgAEAqsCrwADArQCtQAJArYCtgACArcCtwAGArgCuAACArkCuQAGAroCugACArsCuwAGArwCvAATAr0CvQAXAr4CvgATAr8CvwAXAsACwAATAsECwQAXAsICwgATAsMCwwAXAsQCxAABAsYCxgAFAscCxwAIAsgCyAAFAskCyQAIAsoCygAFAssCywAIAswCzAAFAs0CzQAIAs4CzgAFAs8CzwAIAtkC2QAEAuUC5QAKAucC5wALAukC6QAYAusC6wAYAu0C7QAYAu8C7wAYAvIC8gAEAvQC9AAEAvYC9wAEAvgC+AABAvkC+QADAvoC+gABAvsC+wADAvwC/AABAv0C/QADAv8C/wAVAwEDAQAVAwMDAwAVAw4DDgAOAxADEAAOAxIDEgAOAxQDFAAKAxYDFgAKAxgDGAAKAxoDGgAKAxwDHAAKAx4DHgAKAyADIAAbAyIDIgAMAyMDIwAJAyQDJAAMAyUDJQARAyYDJgAQAycDJwARAygDKAAQAykDKQARAyoDKgAQAy8DMAANAzEDMQAjAzIDOAAPA0MDRwANA00DTwAPA1QDVAANA2UDZQAeA2YDaQAkA20DbwANA3gDeAAjA4YDhgACA4cDhwAFA4oDigABA4sDiwAMA44DjgACA48DjwAcA5ADkAAFA5EDkQARA5QDlAALA5cDlwABA5gDmAAWA5kDmQAOA5oDmgAMA5sDmwASA50DnQAMA6ADoAAEA6QDpAADA6YDpgAJA6oDqgADA60DrQAFA64DrgAiA7IDsgAKA7MDtAALA7UDtQAlA7YDtgACA7cDtwAcA7gDuAAiA7kDuQAFA70DvQABA78DvwAWA8ADwAATA8EDwQAOA8IDwgASA8MDwwAGA8QDxAAIA8YDxgADA8cDxwAHA8gDyAAXA8kDyQAJA8oDygAUA8sDywAIA8wDzAAaA9ID0gAJA9MD0wAbA9UD1QAbA9cD1wAbA9kD2QAMA9oD2gAJA9sD3AAZA98D3wAZA+ED4QAEA+ID4gACA+MD4wAGA+QD5AAFA+YD5gAIA+oD6gAdA+sD6wAJA/AD8AATA/ED8QAXA/ID8gAMA/MD8wAJA/UD9QASA/YD9gAUA/gD+AACA/kD+QAGA/oD+gACA/sD+wAGA/4D/gAFA/8D/wAIBAEEAgAIBAMEAwASBAQEBAAUBAsECwABBAwEDAADBBAEEAADBBIEEgAHBBMEEwAlBBQEFAAJBBUEFQAlBBYEFgAJBBcEFwAlBBgEGAAJBB4EHgACBB8EHwAGBCAEIAACBCEEIQAGBCIEIgACBCMEIwAGBCQEJAACBCUEJQAGBCYEJgACBCcEJwAGBCgEKAACBCkEKQAGBCoEKgACBCsEKwAGBCwELAACBC0ELQAGBC4ELgACBC8ELwAGBDAEMAACBDEEMQAGBDIEMgACBDMEMwAGBDQENAACBDUENQAGBDYENgAFBDcENwAIBDgEOAAFBDkEOQAIBDoEOgAFBDsEOwAIBDwEPAAFBD0EPQAIBD4EPgAFBD8EPwAIBEAEQAAFBEEEQQAIBEIEQgAFBEMEQwAIBEQERAAFBEUERQAIBEoESgABBEsESwADBEwETAABBE0ETQADBE4ETgABBE8ETwADBFAEUAABBFEEUQADBFIEUgABBFMEUwADBFQEVAABBFUEVQADBFYEVgABBFcEVwADBF8EXwADBGIEYgAKBGQEZAAKBHAEcAAMBHEEcQAJBHIEcgAMBHMEcwAJBHQEdAAMBHUEdQAJBHcEdwAOBHsEewAiBHwEfAAaBH8EfwAEBIEEgQAgBIIEggAiBIQEhAALBIYEhgAMBJgEmAAEBJkEmQACBJoEmgAGBJsEmwAFBJ8EnwABBKAEoAADBKIEogAVBKYEpgAcBKcEpwAHBKgEqAABBKoEqgABBK0ErQAEBK4ErgALBLAEsAALBLIEsgAYBLUEtQAEBLcEtwAEBLgEuAABBLkEuQAWBLoEugAHBLwEvAAVBL8EvwAOBMEEwQAKBMIEwgAdBMMEwwAJBMQExAAdBMUExQAJBMYExgAbBMgEyAARBMkEyQAQBMoEygABBMsEywAPBM8EzwANBNIE0gAPBNgE2AAeBN0E3QAjBOgE6AAeBOoE6gAPBPEE8QANBPUE9QAjAAEABgT1ABQAAAAAAAAAAAAUAAAAAAAAAAAAGgAfABoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABgAAAAIAAAAAAAAAAgAAAAAAIwAAAAAAAAAAAAIAAAACAAAAEAALAAoAHQAWABEADAATAAAAAAAAAAAAAAAAAAcAAAABAAEAAQAAAAEAAAAAAAAAAAAAAAMAAwAEAAMAAQAAAA4AAAAFAAkAAAAVAAkADwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAQAAAAAAAAACAAEAAAAFAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABgACAAYAAAAAAAAAAAAAAAAAAQAAAAkAAAAAAAAAAwAAAAAAAAAAAAAAAAABAAEAAAAFAAAAAAAAAAAAAAAAAAsAAgAZAAAACwAAAAAAAAARAAAAAAAZACIAAAAAACAAAAAAAAAAAAAAAAAAAAAAAAAAAAADAAAAFQAAAAMAAwAbAAMAAwADAAAAAQADACEAAwADAAAAAAADAAAAAwAAAAAAAQAbAAMAAAAAAAIAAAAAAAAAAAAGAAAAAAAAAAAAAAAAAAAAAAAAAAIABAAdAAkAAgAAAAIAAQACAAAAAgABAAAAAAAAAAAAAAAAAAAAAAADAAAAAAAAAAAAAAAAAAAAEQAVAAAAAwAAAAAACwAAAAAAAwAAAAMAAAAAAAIAAQARABUACwAAACAAIQAAAAAAAAAAAAAAAAAAABkAGwAAAAMAAAADAAAAAwAAAAAAAAAAAAMAEQAVAAAAAQABAAAAAAAAAAAAGQAAAAAAAAACAAEAAAAAAAAAGQAbAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAHwAfAAAAFAAUABoAFAAUABQAGgAAAAAAAAAaABoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAOAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAXABwAJAAAABIAGAAeAAAACAAAAAgAAAAAAAAAAAAAAAAAAAAIAAAAAAAAAAAAAAANAAgADQAAAAAAAAAAAAAAAAAYAAgAAAAAABgAAAAAAAAAHAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABwAAAAAABgACAAXABwAGAAAAAAAAAAAAAAACAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAANAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACAAIAAAACAAIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAB8AAAAAAAAAAAAAAAYABgAGAAYABgAGAAYAAgAAAAAAAAAAAAAAAAAAAAAAAAACAAIAAgACAAIACgAKAAoACgAMAAcABwAHAAcABwAHAAcAAQABAAEAAQABAAAAAAAAAAAAAwAEAAQABAAEAAQABQAFAAUABQAJAAkABgAHAAYABwAGAAcAAgABAAIAAQACAAEAAgABAAAAAQAAAAEAAAABAAAAAQAAAAEAAAABAAIAAQACAAEAAgABAAIAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwAAAAMAAAADAAMAAgAEAAIABAACAAQAAAAAAAAAAAAAAAAAEAAOABAADgAQAA4AEAAOABAADgALAAAACwAAAAsAAAAKAAUACgAFAAoABQAKAAUACgAFAAoABQAWAAAADAAJAAwAEwAPABMADwATAA8AAAAAAAIAAAAAAAAAAAANAA0ADQANAA0ADQANAAgAAAAAAAAAAAAAAAAAAAAAAAAACAAIAAgACAAIABIAEgASABIAFwANAA0ADQAIAAgACAAIAAAAAAAAAAAAAAAAAAgACAAIAAgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACAAIAAgAAAAAAAAAHgAeAB4AHgAAABgAAAASABIAEgASABIAEgAkABcAFwAAAAAAAAAGAAAAAAAAAAIADAAAAAAABgAAAAAAEwAAAAAAAAAAAAAAAgAAAAAADAARAAAADAABAAAAAwAAAAUAAAAEAAAACQAAAAAABQAEAAUAAAAAAAAAAAAAAAAAIwAAAAAAIgAGAAAAAAAAAAAAAAAAAAIAAAAAAAIACwARAAcAAQADAAQAAwABAAkAFQABAAMADgAAAAAAAAADAAkAFgAAABYAAAAWAAAADAAJABQAFAAAAAAAFAAAAAMABgAHAAAAAAABAAMAAAAAAB0ACQABAAIAAAAAAAIAAQAMAAkAAAARABUAAAAGAAcABgAHAAAAAAAAAAEAAAABAAEAEQAVAAAAAAAAAAMAAAADAAIABAACAAEAAgAEAAAAAAAiAAkAIgAJACIACQAgACEAAAADAAEABgAHAAYABwAGAAcABgAHAAYABwAGAAcABgAHAAYABwAGAAcABgAHAAYABwAGAAcAAAABAAAAAQAAAAEAAAABAAAAAQAAAAEAAAABAAAAAQAAAAAAAAAAAAIABAACAAQAAgAEAAIABAACAAQAAgAEAAIABAACAAEAAgABAAIAAQACAAQAAgABAAoABQAKAAUAAAAFAAAABQAAAAUAAAAFAAAABQAMAAkADAAJAAwACQAAAAsAAAAgACEAAAADAAAAAAAAAAAAAAAAAAAAAAAAAAwAAAAAAAAAAAAAAB8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAMABgAHAAAAAQAAAAAAAgAEAAAAAAAAAAUAAAAAAAAAAAABAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAADAAAAAwACAAAAAAAAAAAAEAAOAAsAAAAKAB0ACQAdAAkAFgAAABMADwAAAA0AAAAAAAAACAAXAAAADQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFwAcAAAAFwAAAAAAAAAAAAAAAAAAAAAADQAAAAAAAAAAAAAAAAAIAAAAAAAIABgAHAAAAAAACAAXAAAAAQAAAAoBYgKSAARERkxUABpjeXJsABpncmVrABpsYXRuAEgABAAAAAD//wASAAAAAQACAAMABAAIAAwADQAOAA8AEAARABIAEwAUABUAFgAXAC4AB0FaRSAA5ENSVCAA5EZSQSAAWk1PTCAAtk5BViAAiFJPTSAAtlRSSyAA5AAA//8AEwAAAAEAAgADAAQABwAIAAwADQAOAA8AEAARABIAEwAUABUAFgAXAAD//wAUAAAAAQACAAMABAAGAAgACQAMAA0ADgAPABAAEQASABMAFAAVABYAFwAA//8AFAAAAAEAAgADAAQABgAIAAsADAANAA4ADwAQABEAEgATABQAFQAWABcAAP//ABQAAAABAAIAAwAEAAYACAAKAAwADQAOAA8AEAARABIAEwAUABUAFgAXAAD//wATAAAAAQACAAMABAAFAAgADAANAA4ADwAQABEAEgATABQAFQAWABcAGGMyc2MAkmNjbXAAmGRsaWcAoGRub20ApmZyYWMArGxpZ2EAtmxpZ2EAvGxpZ2EAyGxudW0A0GxvY2wA1mxvY2wA3GxvY2wA4m51bXIA6G9udW0A7nBudW0A9HNtY3AA+nNzMDEBAHNzMDIBBnNzMDMBDHNzMDQBEnNzMDUBGHNzMDYBHnNzMDcBJHRudW0BKgAAAAEAAAAAAAIAAgAEAAAAAQAKAAAAAQAYAAAAAwAWABcAGQAAAAEACQAAAAQACAAJAAgACQAAAAIACAAJAAAAAQAVAAAAAQAHAAAAAQAFAAAAAQAGAAAAAQAZAAAAAQASAAAAAQATAAAAAQABAAAAAQALAAAAAQAMAAAAAQANAAAAAQAOAAAAAQAPAAAAAQAQAAAAAQARAAAAAQAUABoANgQwB+4IoAjKD24PhA+uD8IP5hAQEEwQYBB0EIgQmhC0EPYRFBFmEawSDhJsEoASsBLSAAEAAAABAAgAAgH6APoB5wJxAdEB0AHPAc4BzQHMAcsBygHJAcgCMwIyAjECMAIoAeYB5QHkAeMB4gHhAeAB3wHeAd0B3AHbAdoB2QHYAdcB1gHVAdQB0wHSAegB6QJzAnUCdAJ2AnICdwJSAeoB6wHsAe0B7gHvAfAB8QHyAfMB9AH1AfYB9wH4AfkB+gH7AfwB/QH+AgACAQT+AgICAwIEAgUCBgIHAggCCQIKAgsCOwINAg4CDwIQBPgCEQITAhQCFQIWAhcCGAIZAhsCHAIeAh0DLwMwAzEDMgMzAzQDNQM2AzcDOAM5AzoDOwM8Az0DPgM/A0ADQQNCA0MDRANFA0YDRwNIA0kDSgNLA0wDTQNOA08DUANRA1IDUwNUA1UDVgNXA1gDWQNaA1sDXANdA14DXwNgA2EDYgNjBP8DZANlA2YDZwNoA2kDagNrA2wDbQNuA28DcANxA3IDcwN0A3UFAgN2A3cDeQN4A3oDewN8A30DfgN/A4ADgQOCA4MDhAOFBQAFAQTLBMwEzQTOBM8E0ATRBNIE0wTUBNUE1gTXBNgE2QTaBNsE3ATdBN4E3wTgBOEE4gTjBOQE5QTmBOcB/wToBOkE6gTrBOwE7QTuBO8E8ATxBPIE8wT0BPUE9gUDBQQFBQUGBPcE+QT6BPwCGgT9BPsCDAISBQsFDAABAPoACAAKABQAFQAWABcAGAAZABoAGwAcAB0AJQAmACcAKAApACoAKwAsAC0ALgAvADAAMQAyADMANAA1ADYANwA4ADkAOgA7ADwAPQA+AGUAZwCBAIMAhACMAI8AkQCTALEAsgCzALQAtQC2ALcAuAC5ALoA0gDTANQA1QDWANcA2ADZANoA2wDcAN0A3gDfAOAA4QDiAOMA5ADlAOYA5wDoAOkBLwEzATUBNwE5ATsBQQFDAUUBSQFLAUwBWAFZAZcBnQGiAaUCegJ7An0CfwKAAoECggKDAoQChQKGAocCiAKJAooCiwKMAo0CjgKPApACkQKSApMClAKVApYClwKYApkCtgK4AroCvAK+AsACwgLEAsYCyALKAswCzgLQAtIC1ALWAtgC2gLcAt4C4ALiAuMC5QLnAukC6wLtAu8C8QLzAvUC+AL6AvwC/gMAAwIDBAMGAwgDCgMMAw4DEAMSAxQDFgMYAxoDHAMeAyADIgMkAyUDJwMpAysDLQOGA4cDiAOJA4oDiwOMA44DjwOQA5EDkgOTA5QDlQOWA5cDmAOZA5oDmwOcA50DrQOuA68DsAOxA7IDswO0A7UDtgO3A7gDuQO6A7sDvAO9A74DvwPAA8EDwgPTA9UD1wPZA+4D8APyBAcEDQQTBH0EggSGBQcFCQABAAAAAQAIAAIB3ADrAnECMwIyAjECMAIoAeYB5QHkAeMB4gHhAeAB3wHeAd0B3AHbAdoB2QHYAdcB1gHVAdQB0wHSAmQCcwMwAnUCdAMvAeMCcgJ3AlIE0gTTAeoB6wTUBNUE1gHsBNcB7QHuAe8E3AHwAfAE3QTeAfEB8gHzAfoE6wTsAfsB/AH9Af4B/wIABO8E8ATyBPUE/gICAgMCBAIFAgYCBwIIAgkCCgILAfQB9QH2AfcB+AH5AjsCDQIOAg8CEAT4AhECEwIUAhUCFwIZAnYDMQMyAzMDNAM1AzYDNwM4AzkDOgM7AzwDPQM+Az8DQANBA0IDQwNEA0UDRgNHA0gDSQNKA0sDTAOCA00DTgNPA1ADUQNSA1MDVANVA1YDVwNYA1kDWgNbA1wDXQNeA18DYANhA2IE/wNkA2UDZgNnA2gDaQNqA2sDbANtA24DbwNwA3EDcgNzA3QDdQUCA3YDdwN5A3gDegN7A3wDfQN+A38DgAOBA4MDhAOFBQAFAQTLBMwEzQTOBNgE2wTZBNoE3wTgBOEEzwTQBNEE6gTtBO4E8QTzBPQCAQT2BOIE4wTkBOUE5gTnBOgE6QUDBQQFBQUGBPcE+QT6AhgE/AIaBP0E+wIWAgwCEgULBQwAAQDrAAoARQBGAEcASABJAEoASwBMAE0ATgBPAFAAUQBSAFMAVABVAFYAVwBYAFkAWgBbAFwAXQBeAIUAhgCHAIkAigCLAI0AkACSAJQAuwC8AL0AvgC/AMAAwQDCAMMAxADFAMYAxwDIAMkAygDLAMwAzQDOAOoA6wDsAO0A7gDvAPAA8QDyAPMA9AD1APYA9wD4APkA+gD7APwA/QD+AP8BAAEBAQIBAwEEAQUBBgEHATABNAE2ATgBOgE8AUIBRAFGAUoBTQFaAnwCfgKaApsCnAKdAp4CnwKgAqECogKjAqQCpQKmAqcCqAKpAqoCqwKsAq0CrgKvArACsQKyArMCtAK1ArcCuQK7Ar0CvwLBAsMCxQLHAskCywLNAs8C0QLTAtUC1wLZAtsC3QLfAuEC5ALmAugC6gLsAu4C8ALyAvQC9gL5AvsC/QL/AwEDAwMFAwcDCQMLAw0DDwMRAxMDFQMXAxkDGwMdAx8DIQMjAyYDKAMqAywDLgOeA58DoAOhA6MDpAOlA6YDpwOoA6kDqgOrA6wDwwPEA8UDxgPHA8gDyQPKA8sDzAPNA84DzwPQA9ED0gPUA9YD2APaA+8D8QPzBAEECAQOBBQEfgR/BIMEhwUIBQoABgAAAAYAEgAqAEIAWgByAIoAAwAAAAEAEgABAJAAAQAAAAMAAQABAE0AAwAAAAEAEgABAHgAAQAAAAMAAQABAE4AAwAAAAEAEgABAGAAAQAAAAMAAQABAuEAAwAAAAEAEgABAEgAAQAAAAMAAQABA84AAwAAAAEAEgABADAAAQAAAAMAAQABA9AAAwAAAAEAEgABABgAAQAAAAMAAQABBEkAAgACAKgArAAAASQBJwAFAAEAAAABAAgAAgASAAYCYQJfAmICYwJgBQ0AAQAGAE0ATgLhA84D0ARJAAQAAAABAAgAAQYyADYAcgCkAK4AuADKAPwBDgEYAUoBZAF+AZABugH2AgACIgI8Ak4CigKcArYC4ALyAyQDLgM4A0oDfAOGA5ADmgO0A84D4AQKBDwERgRoBIIElATGBNgE8gUcBS4FOAVCBUwFVgWABaoF1AX+BigABgAOABQAGgAgACYALAKAAAIAqQQeAAIArQJ/AAIAqAQgAAIAqwKCAAIAqgSZAAIArAABAAQEpgACAK0AAQAEArwAAgCpAAIABgAMBKoAAgG6BKgAAgCtAAYADgAUABoAIAAmACwCiAACAKkENgACAK0ChwACAKgEOAACAKsEOgACAKoEmwACAKwAAgAGAAwElQACAKkC1gACAboAAQAEBKwAAgCtAAYADgAUABoAIAAmACwCjAACAKkESAACAK0CiwACAKgERgACAKsC2gACAKoEnQACAKwAAwAIAA4AFASuAAIAqQLnAAIBugSwAAIArQADAAgADgAUAukAAgCpAusAAgG6BLIAAgCtAAIABgAMA+AAAgCpBLQAAgCtAAUADAASABgAHgAkAvEAAgCpAvMAAgG6BLYAAgCtBJcAAgCoAo8AAgCqAAcAEAAYAB4AJAAqADAANgS4AAMAqgCpApEAAgCpBEoAAgCtApAAAgCoBEwAAgCrApMAAgCqBJ8AAgCsAAEABAS5AAIAqQAEAAoAEAAWABwC/gACAKkDAAACAboEuwACAK0EoQACAKwAAwAIAA4AFAMEAAIAqQMKAAIBugS9AAIArQACAAYADAMOAAIBugS/AAIArQAHABAAGAAeACQAKgAwADYEwQADAKoAqQKWAAIAqQRiAAIArQKVAAIAqARkAAIAqwMUAAIAqgSjAAIArAACAAYADATEAAIArQTCAAIAqgADAAgADgAUA9UAAgCpBMYAAgCtA9MAAgCoAAUADAASABgAHgAkApkAAgCpBHAAAgCtA9kAAgCoBHIAAgCrBHQAAgCqAAIABgAMAyUAAgCpBMgAAgCtAAYADgAUABoAIAAmACwCmwACAKkEHwACAK0CmgACAKgEIQACAKsCnQACAKoEmgACAKwAAQAEBKcAAgCtAAEABAK9AAIAqQACAAYADASrAAIBugSpAAIArQAGAA4AFAAaACAAJgAsAqMAAgCpBDcAAgCtAqIAAgCoBDkAAgCrBDsAAgCqBJwAAgCsAAEABASWAAIAqQABAAQErQACAK0AAQAEBEkAAgCtAAMACAAOABQErwACAKkC6AACAboEsQACAK0AAwAIAA4AFALqAAIAqQLsAAIBugSzAAIArQACAAYADAPhAAIAqQS1AAIArQAFAAwAEgAYAB4AJALyAAIAqQL0AAIBugS3AAIArQSYAAIAqAKqAAIAqgAGAA4AFAAaACAAJgAsAqwAAgCpBEsAAgCtAqsAAgCoBE0AAgCrAq4AAgCqBKAAAgCsAAEABAS6AAIAqQAEAAoAEAAWABwC/wACAKkDAQACAboEvAACAK0EogACAKwAAwAIAA4AFAMFAAIAqQMLAAIBugS+AAIArQACAAYADAMPAAIBugTAAAIArQAGAA4AFAAaACAAJgAsArEAAgCpBGMAAgCtArAAAgCoBGUAAgCrAxUAAgCqBKQAAgCsAAIABgAMBMUAAgCtBMMAAgCqAAMACAAOABQD1gACAKkExwACAK0D1AACAKgABQAMABIAGAAeACQCtAACAKkEcQACAK0D2gACAKgEcwACAKsEdQACAKoAAgAGAAwDJgACAKkEyQACAK0AAQAEAysAAgCpAAEABAMtAAIAqQABAAQDLAACAKkAAQAEAy4AAgCpAAUADAASABgAHgAkAqcAAgCpAqYAAgCoBEcAAgCrAtsAAgCqBJ4AAgCsAAUADAASABgAHgAkBFgAAgCpBGAAAgCtBFoAAgCoBFwAAgCrBF4AAgCqAAUADAASABgAHgAkBFkAAgCpBGEAAgCtBFsAAgCoBF0AAgCrBF8AAgCqAAUADAASABgAHgAkBGYAAgCpBG4AAgCtBGgAAgCoBGoAAgCrBGwAAgCqAAUADAASABgAHgAkBGcAAgCpBG8AAgCtBGkAAgCoBGsAAgCrBG0AAgCqAAEABASlAAIAqQACABEAJQApAAAAKwAtAAUALwA0AAgANgA7AA4APQA+ABQARQBJABYASwBNABsATwBUAB4AVgBbACQAXQBeACoAgQCBACwAgwCDAC0AhgCGAC4AiQCJAC8AjQCNADAAmACbADEA0ADQADUAAQAAAAEACAABAAYAAgABAAIDCAMJAAEAAAABAAgAAgASAAYFBwUIBQkFCgULBQwAAQAGAroCuwLMAs0DTwNYAAEAAAABAAgAAQAGAAEAAQABAXsABAAAAAEACAABAEAAAQAIAAIABgAOAb4AAwBKAE0BvAACAE0ABAAAAAEACAABABwAAQAIAAIABgAOAb8AAwBKAFABvQACAFAAAQABAEoABAAAAAEACAABACoAAwAMABYAIAABAAQBuwACAEoAAQAEAcEAAgBYAAEABAHAAAIAWAABAAMASgBXAJUAAQAAAAEACAABAAYB3gABAAEASwABAAAAAQAIAAEABgFvAAEAAQC7AAEAAAABAAgAAQAGAfUAAQABADYAAQAAAAEACAACABwAAgIsAi0AAQAAAAEACAACAAoAAgIuAi8AAQACAC8ATwABAAAAAQAIAAIAHgAMAkUCRwJGAkgCSQJnAmgCaQJqAmsCbAJtAAEADAAnACgAKwAzADUARgBHAEgASwBTAFQAVQABAAAAAQAIAAIADAADAm4CbwJvAAEAAwBJAEsCagABAAAAAQAIAAIALgAUAloCXgJYAlUCVwJWAlsCWQJdAlwCTwJKAksCTAJNAk4AGgAcAlMCZQACAAQAFAAdAAACZgJmAAoCcAJwAAsEjQSUAAwAAQAAAAEACAACAC4AFASUAnAEjQSOBI8EkASRAmYEkgSTAkwCTgJNAksCTwJlABoCUwAcAkoAAgACABQAHQAAAlUCXgAKAAEAAAABAAgAAgAuABQCWwJdAl4CWAJVAlcCVgJZAlwCWgAbABUAFgAXABgAGQAaABwAHQAUAAEAFAAaABwCSgJLAkwCTQJOAk8CUwJlAmYCcASNBI4EjwSQBJEEkgSTBJQAAQAAAAEACAACAC4AFASRBJICcASNBI4EjwSQAmYEkwAXABkAGAAWABsAFAAaAB0AHAAVBJQAAgAGABoAGgAAABwAHAABAkoCTwACAlMCUwAIAlUCXgAJAmUCZQATAAEAAAABAAgAAQAGAYEAAQABABMABgAAAAEACAADAAEAEgABAGwAAAABAAAAGAACAAMBlAGUAAABxQHHAAECHwIlAAQAAQAAAAEACAACADwACgHHAcYBxQIfAiACIQIiAiMCJAIlAAEAAAABAAgAAgAaAAoCPgB6AHMAdAI/AkACQQJCAkMCRAACAAEAFAAdAAA=",
  "Roboto-Regular.ttf": "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"
};
(function () {
    'use strict';

    angular.module('su-fiscalizacao', [
        'su-componentes',
        'ngMaterialDatePicker',
        'ciMapaDirectiveFiscalizacao',
        'chart.js',
    ]).config(["$httpProvider", function ($httpProvider) {
        $httpProvider.interceptors.push('errorInterceptorFiscalizacao');
        $httpProvider.interceptors.push('successInterceptorFiscalizacao');
    }]);

})();

(function () {
    "use strict";
    angular
        .module("su-fiscalizacao")
        .directive("numerosei", ["$http", directive]);

        

    var sancaoBaseUrl = "/fiscalizacao/api/sancao";

    function directive($http) {
        return {
            require: "ngModel",
            link: function (scope, elm, attrs, controller) {
                controller.$asyncValidators.numerosei = function (modelValue) {
                    if (controller.$isEmpty(modelValue)) {
                        return Promise.resolve();
                    }

                    return $http
                        .get(
                            sancaoBaseUrl + "/consultar-documento/" + modelValue
                        )
                        .then(function (response) {
                            var promise = new Promise(function (
                                resolve,
                                reject
                            ) {
                                if (
                                    response.data &&
                                    response.data.idDocumento
                                ) {
                                    resolve();
                                } else {
                                    reject();
                                }
                            });

                            return promise;
                        })
                        .catch(function () {
                            return Promise.reject();
                        });
                };
            }
        };
    }
})();

(function () {
    "use strict";
    angular.module("su-fiscalizacao").factory("SuspenderNotificacaoService", [
        "$http",
        "Upload",
        function ($http, Upload) {
            var baseUrl = "/fiscalizacao/api/suspender/notificacao";
            var documentoBaseUrl = "/fiscalizacao/api/documentos";
            var uploadUrl = "/fiscalizacao/api/arquivo";

            var listarTiposNotificacao = function (statusSancao) {
                return $http.get(
                    baseUrl + "/tipos/" + statusSancao
                );
            };

            var atualizarSuspensaoNotificacao = function(suspensao) {
                return $http.put(
                    baseUrl + '/' + suspensao.id,
                suspensao);
            }

            var buscarSuspensaoPorIdDocumento = function(documentoId) {
                return $http.get(
                    baseUrl + '/buscaPorDocumento/' + documentoId
                );
            }

            var criarSuspensaoNotificacao = function(suspensao) {
                return $http.post(
                    baseUrl + '/inicializacao',
                suspensao)
            }

            var buscarNotificacao = function (documentoId) {
                return $http.get(documentoBaseUrl + "/" + documentoId);
            };


            var autuar = function(registro) {
                return $http.post(baseUrl + '/auto', registro)
            }

            var salvar = function(registro) {
                return $http.post(baseUrl, registro)
            }

            var alterarStatusNotificacao = function(registro) {
                return $http.put(baseUrl + '/status', registro);   
            }

            var buscarRelatorioFiscalizacao = function(documento) {
                return $http.get(baseUrl + '/' + documento.id + '/relatorio-fiscalizacao');
            }

            var upload = function ($file) {
                return Upload.upload({
                    url: uploadUrl + "/upload",
                    data: {
                        file: $file
                    }
                });
            };

           
            return {
                autuar: autuar,
                salvar: salvar,
                alterarStatusNotificacao: alterarStatusNotificacao,
                listarTiposNotificacao: listarTiposNotificacao,
                buscarNotificacao: buscarNotificacao,
                upload: upload,
                buscarRelatorioFiscalizacao: buscarRelatorioFiscalizacao,
                buscarSuspensaoPorIdDocumento: buscarSuspensaoPorIdDocumento,
                atualizarSuspensaoNotificacao: atualizarSuspensaoNotificacao,
                criarSuspensaoNotificacao: criarSuspensaoNotificacao
            };
        }
    ]);
})();

(function() {
  'use strict';
  angular.module('su-fiscalizacao')
  .controller('SuspenderNotificacaoController',[
    '$scope',
    '$state',
    '$stateParams',
    '$timeout',
    'ComumFactory',
    'SuspenderNotificacaoService',
    controller
  ]);

  var isUndefinedOrNull = function(value) {
    return angular.isUndefined(value) || value === null;
  }

  function controller($scope, $state, $stateParams, $timeout, ComumFactory, service) {
    var scope = this;

    scope.init = function() {
      scope.buscarNotificacao(function() {
        scope.buscarSuspensao(function() {
          scope.buscarTiposNotificacao(function() {
            if(scope.suspensao && scope.suspensao.id) {
              scope.isEditar = true;
              scope.somenteLeitura = false;
              scope.mostrarFormTipoNotificacao = false;
              scope.adicionarObservadorDeAtualizacao();
            } else {
              scope.tipoNotificacaoSomenteLeitura = true;
              scope.tipoNotificacaoSelecionado = null;
              scope.tiposNotificacao = [];
              scope.buscarTiposNotificacao(function() {
                scope.tipoNotificacaoSomenteLeitura = false;
                scope.somenteLeitura = false;
                scope.isEditar = false;
                scope.mostrarFormTipoNotificacao = true;
              });
            }
          })
        })
      });

      scope.operacoes = {
        gerarDespacho: null,
        tipoDespacho: null,
        novoStatus: null
      }
    }


    scope.mostrarBotaoSalvar = {
      formGestaoPraias : function() {
        if(scope.suspensao.acaoMunicipioCompativel == true && scope.suspensao.numeroDocumentoSei) {
          scope.configurarEstado('INATIVO');
          return true;
        } else if(scope.suspensao.acaoMunicipioCompativel == false) {
          if(scope.suspensao.numeroDocumentoSei) {
            scope.configurarEstado('INATIVO');
          } else {
            scope.configurarEstado('PENDENTE_DE_COMUNICACAO');
          }
          return true;

        } else {
          return false;
        }
      },
      formDesocupacaoImediataOuTrintaNovamentaDias: function() {
        if(scope.suspensao.autoTutelaCabivel == true && !scope.suspensao.arquivoComprobatorio) {
          scope.configurarEstado('AGUARDANDO_DESOCUPACAO');
          return true;
        } else if(scope.suspensao.processoAnalisadoAprovado == false) {
          scope.configurarEstado('ATIVO', null, false, false);
          return true;
        } else {
          return false;
        }
      }
    }

    scope.mostrarBotaoAutuar = {
      formGestaoPraias: function() {
        if(scope.suspensao.houveAcaoMunicipio == false) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else {
          return false;
        }
      },
      formApresentacaoDocumentos: function() {
        if(scope.suspensao.questaoSanada == true) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else if(scope.suspensao.indicioInfracaoPatrimonio == false && scope.suspensao.numeroDocumentoSei) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else if(scope.suspensao.notificadoApresentouEsclarecimentos == false) {
          scope.configurarEstado('INATIVO', 'DESPACHO_AUSENCIA_ESCLARECIMENTOS',true, false, true);
          return true;
        } else {
          return false;
        }
      },
      formApresentacaoDocumentosRegularizacao: function() {
        if(scope.suspensao.notificadoApresentouDocumentacao == true) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else if(scope.suspensao.indicioInfracaoPatrimonio == false) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else {
          return false;
        }
      },
      formApresentacaoDocumentosOrgaosEntidades: function() {
        if(scope.suspensao.notificacaoAnulada == true && scope.suspensao.numeroDocumentoSei) {
          scope.configurarEstado('CANCELADO', null , false);
          return true;
        } else if(scope.suspensao.notificacaoAnulada == false && scope.suspensao.numeroDocumentoSei) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else if(scope.suspensao.novaVistoria == false && scope.suspensao.arquivoComprobatorio && scope.suspensao.justificativaMotivacao) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else if(scope.suspensao.encargosCumpridos == false || scope.suspensao.encargosCumpridos == true) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else if(scope.suspensao.indicioEncargosCumpridos == false) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else {
          return false;
        }
      },
      formDesocupacaoImediataOuTrintaNovamentaDias: function() {
        if(scope.suspensao.notificacaoAnulada == true && scope.suspensao.numeroDocumentoSei) {
          scope.configurarEstado('CANCELADO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else if(scope.suspensao.notificacaoAnulada == false && scope.suspensao.numeroDocumentoSei) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else if(scope.suspensao.decisaoParalizarPrazoDesocupacao == true && scope.suspensao.numeroDocumentoSei) {
          scope.configurarEstado('PARALISADO', null, false);
          return true;
        } else if(scope.suspensao.ocorreuDesocupacao == true) {
          scope.configurarEstado('INATIVO', null, false);
          return true;
        } else if(scope.suspensao.autoTutelaCabivel == true &&
          scope.suspensao.arquivoComprobatorio) {
          scope.configurarEstado('INATIVO', 'DESPACHO_ENCAMINHAMENTO', true);
          return true;
        } else if(scope.suspensao.autoTutelaCabivel == false) {
          scope.configurarEstado('ENVIADO_A_AGU', 'DESPACHO_ENCAMINHAMENTO_AGU', true);
          return true;
        } else if(scope.suspensao.novaVistoria == false) {
          scope.configurarEstado('INATIVO');
          return true;
        } else if(scope.suspensao.processoAnalisadoAprovado == true) {
          scope.configurarEstado('INATIVO');
          return true;
        }
      }
    }

    // Verifica mapeamento entre o form e scope.suspensao para manter alinhamento.
    scope.atualizarForm = function (callback) {
      if (scope.form) {
          $timeout(function () {
              for (var prop in scope.suspensao) {
                if(!isUndefinedOrNull(scope.suspensao[prop]) &&
                  isUndefinedOrNull(scope.form[prop]) &&
                  prop !== 'id' &&
                  prop !== 'idDocumento' &&
                  prop !== 'tipoNotificacao'
                  ) {
                  scope.suspensao[prop] = null;
                }
              }

              if(callback) {
                callback();
              }
          }, 200);
      }
    };

    scope.configurarEstado = function(status, tipoDespacho, gerarDespacho) {
      scope.operacoes.novoStatus = status || 'INATIVO';
      scope.operacoes.tipoDespacho = tipoDespacho;
      scope.operacoes.gerarDespacho = gerarDespacho || false;
    }

    scope.limparEstado = function() {
      scope.operacoes.novoStatus = null;
      scope.operacoes.tipoNotificacao = null;
      scope.operacoes.gerarDespacho = null;
    }

    scope.buscarNotificacao = function(callback) {
      service.buscarNotificacao($stateParams.documentoId)
        .then(function(resposta) {
          scope.notificacao = resposta.data;
          scope.idDocumento = resposta.data.id;

          if(callback) {
            callback();
          }
        });
    }

    scope.buscarSuspensao = function(callback) {
      return service.buscarSuspensaoPorIdDocumento($stateParams.documentoId)
        .then(function(response) {
            var data = response.data;
            scope.suspensao = data;
        }).finally(function() {
          if(callback) {
            callback();
         }
        })
    }

    scope.buscarTiposNotificacao = function (callback) {
        if (scope.notificacao.statusSancao !== 'ATIVO' &&
            scope.notificacao.statusSancao !== 'AGUARDANDO_DESOCUPACAO' &&
            scope.notificacao.statusSancao !== 'PENDENTE_DE_VISTORIA') {
            $state.go('fiscalizacao.gerenciadorInfracao.sancao')
        } else {
            service.listarTiposNotificacao(scope.notificacao.statusSancao)
                .then(function (resposta) {
                    scope.tiposNotificacao = resposta.data;
                    if (callback) {
                        callback();
                    }
                })
        }
    }

    scope.criarSuspensaoNotificacao = function(callback) {
      var suspensao = {
        idDocumento: scope.notificacao.id,
        tipoNotificacao: scope.tipoNotificacaoSelecionado
      }

      service.criarSuspensaoNotificacao(suspensao)
        .then(function(response) {
          scope.suspensao = response.data;

          if(callback) {
            callback();
          }
        })
    }

    scope.autuar = function () {
        if (scope.form.$valid) {
            scope.somenteLeitura = true;

            scope.suspensao.gerarDespacho = scope.operacoes.gerarDespacho;
            scope.suspensao.tipoDespacho = scope.operacoes.tipoDespacho;
            scope.suspensao.novoStatus = scope.operacoes.novoStatus;
            scope.suspensao.arquivoComprobatorio = scope.suspensao.arquivoComprobatorio != null ? scope.suspensao.arquivoComprobatorio.id : null;

            service
                .autuar(scope.suspensao)
                .then(function (response) {
                    if (response.status === 200) {
                        scope.onSucesso("Auto realizado com sucesso.");
                    } else {
                        scope.onErro("Erro ao tentar autuar notificação.");
                        scope.somenteLeitura = false;
                    }
                })
                .catch(function (error) {
                    ComumFactory.mostrarMensagemError(
                        "Erro ao realizar auto: " + error.message
                    );

                    scope.somenteLeitura = false;
                });
        }else{
            ComumFactory.mostrarMensagemError(
                "Necessário preencher os campos obrigatórios"
            );
        }
    };

    scope.salvar = function () {
        if (scope.form.$valid) {
            scope.somenteLeitura = true;

            scope.suspensao.gerarDespacho = scope.operacoes.gerarDespacho;
            scope.suspensao.gerarEspelhoDespacho = scope.operacoes.gerarEspelhoDespacho;
            scope.suspensao.gerarProcesso = scope.operacoes.gerarProcesso;
            scope.suspensao.novoStatus = scope.operacoes.novoStatus;
            
            scope.suspensao.arquivoComprobatorio = scope.suspensao.arquivoComprobatorio ? scope.suspensao.arquivoComprobatorio.id : null;

            service
                .salvar(scope.suspensao)
                .then(function (response) {
                    if (response.status === 200) {
                        scope.onSucesso(
                            "Suspensão de notificação salva com sucesso!"
                        );
                    } else {
                        scope.onErro(
                            "Erro ao salvar suspensão de notificação: " +
                                response.data
                        );
                        scope.somenteLeitura = false;
                    }
                })
                .catch(function (error) {
                    scope.onErro(
                        "Erro ao salvar suspensão de notificação: " +
                            error.message
                    );
                    scope.somenteLeitura = false;
                });
        }else{
            ComumFactory.mostrarMensagemError(
                "Necessário preencher os campos obrigatórios"
            );
        }
    };

    scope.voltar = function() {
      $state.go('fiscalizacao.gerenciadorInfracao.sancao');
    }

    $scope.$onDestroy = function() {
      if(scope.removerObservadorDeAtualizacaoSuspensao) {
        scope.removerObservadorDeAtualizacaoSuspensao();
      }
    }

    scope.adicionarObservadorDeAtualizacao = function() {
      scope.removerObservadorDeAtualizacaoSuspensao = $scope.$watchCollection(function() {
        return scope.suspensao;
      }, function() {
        scope.atualizarForm(scope.atualizarSuspensao);
      });
    }


    scope.mostrarFormSuspensao = function() {
      scope.somenteLeitura = true;

      scope.criarSuspensaoNotificacao(function() {
        scope.adicionarObservadorDeAtualizacao();

        scope.mostrarFormTipoNotificacao = false;
        scope.somenteLeitura = false;
      })

      
    }

    scope.incluirNovaDemanda = function(status) {
      scope.somenteLeitura = true;
      scope.suspensao.arquivoComprobatorio = scope.suspensao.arquivoComprobatorio ? scope.suspensao.arquivoComprobatorio.id : null;
      scope.suspensao.novoStatus = status;
      scope.suspensao.demandaId = $stateParams.demandaId;

      service.alterarStatusNotificacao(scope.suspensao)
        .then(function(response) {
          if(response.status === 200) {
            $state.go('fiscalizacao.demanda.incluir')
          } else {
            scope.onErro('Erro ao alterar status de notificação.');
            scope.somenteLeitura = false;
          }
        });
    }

    scope.editarRelatorio = function(status) {
      scope.somenteLeitura = true;
      scope.suspensao.arquivoComprobatorio = scope.suspensao.arquivoComprobatorio ? scope.suspensao.arquivoComprobatorio.id : null;
      scope.suspensao.novoStatus = status;

      service.alterarStatusNotificacao(scope.suspensao)
        .then(function(response) {
          if(response.status === 200) {
            service.buscarRelatorioFiscalizacao(scope.notificacao)
              .then(function(_response) {
                if(_response.status === 200) {
                  $state.go('fiscalizacao.relatorio.alterar', { id: _response.data.id, focarAbaAutuado: true});
                } else {
                  ComumFactory.mostrarMensagemErro('Erro ao buscar relatório de fiscalização: ' + _response.data);
                  scope.somenteLeitura = false;
                }
              }).catch(function(err) {
                ComumFactory.mostrarMensagemError('Erro ao buscar relatório de fiscalização: ' + err.message);
                scope.somenteLeitura = false;
              })
          } else {
            ComumFactory.mostrarMensagemErro('Erro ao alterar status de notificação');
            scope.somenteLeitura = false;
          }
        });
    }

    scope.onSucesso = function(mensagem) {
      ComumFactory.mostrarMensagemSucesso(mensagem);
      $state.go('fiscalizacao.gerenciadorInfracao.sancao');
    }

    scope.onErro = function(mensagem) {
      ComumFactory.mostrarMensagemErro(mensagem);
    }

    scope.upload = function(file) {
      scope.somenteLeitura = true;
      service.upload(file)
        .then(function(resultado) {
          if(resultado.status === 200) {
            scope.suspensao.arquivoComprobatorio = resultado.data;
          } else {
            ComumFactory.mostrarMensagemErro('Falha ao realizar upload de documento');
          }
          scope.somenteLeitura = false;
        })
    }

    scope.removerArquivoComprobatorio = function() {
      scope.suspensao.arquivoComprobatorio = null;
    }

    scope.atualizarSuspensao = function() {
      if(scope.suspensao != undefined) {
        scope.somenteLeitura = true;

        service.atualizarSuspensaoNotificacao(scope.suspensao)
          .then(function(response) {
            if(response.status === 200) {
              scope.somenteLeitura = false;
            } else {
              ComumFactory.mostrarMensagemErro('Falha ao atualizar suspensão');
            }
          });
      }
    }

    scope.init();
  }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('SuspenderCobrancaController', ['$scope', '$state', '$stateParams', 'SancaoService', 'ComumFactory', controller]);

    function controller($scope, $state, $stateParams, service, ComumFactory) {
        var scope = this;

        scope.init = function () {
            scope.apenasLeitura = true;
            scope._registro = {};
            scope._registro.recursoDeferido = true;
            scope._registro.autoParalizado = true;
            scope._registro.autoCessado = true;
            scope._registro.liminar = true;
            scope._registro.regularizado = true;

            scope.handleLiminarSelected(scope._registro.liminar);
            scope.handleAutoCessadoSelected(scope._registro.autoCessado);
            scope.handleRegistroAnuladoSelected(scope._registro.autoAnulado);
            scope.handleQuestaoUmSelected(scope._registro.recursoDeferido);
            scope.handleRegistroRegularizado(scope._registro.regularizado);

            scope.validaState();
        };

        scope.validaState = function(){
            if($state.current.name === "fiscalizacao.gerenciadorInfracao.detalharCobranca"){
                scope.findBy($stateParams.id, function(){
                    scope.apenasLeitura = false;
                });
            }else{
                scope.apenasLeitura = false;
            }
        };

        scope.persistirRegistro = function(registro){
            registro.idDocumento = $stateParams.documentoId;
          if($scope.formRegistro.$valid){
              service.salvar(registro).then(function(){
                $state.go('fiscalizacao.gerenciadorInfracao.sancao');
              });
          }else{
              ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
          }
        };

        scope.findBy = function(id, callback){
            service.findBy(id).then(function(objectResponse){
                scope._registro = angular.copy(objectResponse.data);

                scope.handleLiminarSelected(scope._registro.liminar);
                scope.handleAutoCessadoSelected(scope._registro.autoCessado);
                scope.handleRegistroAnuladoSelected(scope._registro.autoAnulado);
                scope.handleQuestaoUmSelected(scope._registro.recursoDeferido);
                scope.handleRegistroRegularizado(scope._registro.regularizado);

                if(callback){
                    callback();
                }
            });
        };

        scope.handleRegistroAnuladoSelected = function(value){
          switch(value){
              case true:
                  scope._registro.autoAnulado = true;
                  scope._registro.paralisaDarf = null;
                  break;
              case false:
                  scope._registro.autoAnulado = false;
                  scope._registro.paralisaDarf = true;
                  break;
          }
        };

        scope.handleAutoCessadoSelected = function(value){
            value === true ? scope._registro.cessarDarf = true : scope._registro.cessarDarf = false;
        };

        scope.handleLiminarSelected = function(value){
            value === true ? scope._registro.liminarParalizaDarf = true : scope._registro.liminarParalizaDarf = false;
        };

        /**
         * Caso a resposta da pergunta 1 seja false, então todos os seus campos são limpados
         * @param value
         */
        scope.handleQuestaoUmSelected = function(value){
          if(value === false){
              scope._registro.autoAnulado = null;
              scope._registro.autoAnulado = null;
              scope._registro.processoAnulado = null;
              scope._registro.paralisaDarf = null;
              scope._registro.processoParalizado = null;
          }
        };

        /**
         * Caso a resposta da pergunta 1 seja false, então todos os seus campos são limpados
         * @param value
         */
        scope.handleRegistroRegularizado = function(value){
          if(value === true){
              scope._registro.cancelarDarf = true;
          }else{
              scope._registro.cancelarDarf = null;
              scope._registro.processoRegularizado =  null;
          }
        };

        /**
         * Adiciona o arquivo comprobatório
         * @param file
         */
        scope.addArquivoComprobatorio = function (file) {
            service.upload(file[0]).then(function (objectReturn) {
                scope._registro.arquivo = objectReturn.data;

                scope.apenasLeitura = false;
            });
        };

        scope.consultarDocumento = function(protocoloDocumento, campo){
            scope.consultarDocumentoSei = {};
            scope.consultarDocumentoSei.protocoloDocumento = protocoloDocumento;

            if(scope.consultarDocumentoSei.protocoloDocumento.length === 7){
                scope.apenasLeitura = true;
                service.consultarDocumento(scope.consultarDocumentoSei).then(function(objectResponse){
                    scope.retornoDocumento =  objectResponse.data;
                    switch (campo) {
                        case 'anulado':
                            scope.linkAnulado = scope.retornoDocumento.linkAcesso;
                            break;
                        case 'paralizado':
                            scope.linkParalizado = scope.retornoDocumento.linkAcesso;
                            break;
                        case 'cessado':
                            scope.linkCessado = scope.retornoDocumento.linkAcesso;
                            break;
                        case 'liminar':
                            scope.linkLiminar = scope.retornoDocumento.linkAcesso;
                            break;
                        case 'regularizado':
                            scope.linkRegulularizado = scope.retornoDocumento.linkAcesso;
                            break;
                    }

                    scope.apenasLeitura = false;
                }, function(){
                    ComumFactory.mostrarMensagemError('Documento não encontrado!');
                    scope.retornoDocumento = {};
                    scope.apenasLeitura = false;
                });
            }
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('DetalheSancaoController', ['$scope', '$state', '$stateParams', 'SancaoService', controller]);

    function controller($scope, $state, $stateParams, service) {
        var scope = this;

        scope.init = function () {
            scope.apenasLeitura = true;

            scope.getTiposDocumento(function(){
                scope.getTiposNotificacao(function(){
                    scope.getTiposLogradouro(function(){
                        scope.getDocumentoIdentificacao(function(){
                            scope.findDocumentoById(function(){

                            });
                        });
                    });
                });
            });
        };

        scope.findDocumentoById = function(callback){
            service.getDocumento($stateParams.id).then(function(objectResponse){
                scope.documento = angular.copy(objectResponse.data);

                if(callback){
                    callback();
                }
            });
        };

        scope.getTiposDocumento = function(callback){
            service.getTiposDocumento().then(function(objectResponse){
                scope._tiposDocumento = angular.copy(objectResponse.data);

                if(callback){
                    callback();
                }
            });
        };

        scope.getTiposNotificacao = function(callback){
            service.getTiposNotificacao().then(function(objectResponse){
               scope._tiposNotificacao = angular.copy(objectResponse.data);

                if(callback){
                    callback();
                }
            });
        };

        scope.getTiposLogradouro = function(callback){
            service.getTiposLogradouro().then(function(objectResponse){
                scope._tipoLogradouro = angular.copy(objectResponse.data);

                if(callback){
                    callback();
                }
            });
        };

        scope.getDocumentoIdentificacao = function(callback){
            service.getDocumentoIdentificacao().then(function(objectResponse){
                scope._documentosIdentificacao = angular.copy(objectResponse.data);

                if(callback){
                    callback();
                }
            });
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('DetalheAutoController', ['$scope', '$state', '$stateParams', 'RecursoService', '$mdDialog', 'documentoId', controller]);

    function controller($scope, $state, $stateParams, service, $mdDialog, documentoId) {
        var scope = this;

        scope.init = function () {
            scope.apenasLeitura = true;
            scope.documentoId = documentoId;

            scope.getTiposDocumento(function(){
                scope.getTiposNotificacao(function(){
                    scope.getTiposLogradouro(function(){
                        scope.getDocumentoIdentificacao(function(){
                            scope.findDocumentoById(function(){

                            });
                        });
                    });
                });
            });
        };

        scope.findDocumentoById = function(callback){
            service.getDocumento(scope.documentoId).then(function(objectResponse){
                scope.documento = angular.copy(objectResponse.data);

                if(callback){
                    callback();
                }
            });
        };

        scope.getTiposDocumento = function(callback){
            service.getTiposDocumento().then(function(objectResponse){
                scope._tiposDocumento = angular.copy(objectResponse.data);

                if(callback){
                    callback();
                }
            });
        };

        scope.getTiposNotificacao = function(callback){
            service.getTiposNotificacao().then(function(objectResponse){
               scope._tiposNotificacao = angular.copy(objectResponse.data);

                if(callback){
                    callback();
                }
            });
        };

        scope.getTiposLogradouro = function(callback){
            service.getTiposLogradouro().then(function(objectResponse){
                scope._tipoLogradouro = angular.copy(objectResponse.data);

                if(callback){
                    callback();
                }
            });
        };

        scope.getDocumentoIdentificacao = function(callback){
            service.getDocumentoIdentificacao().then(function(objectResponse){
                scope._documentosIdentificacao = angular.copy(objectResponse.data);

                if(callback){
                    callback();
                }
            });
        };

        scope.cancelModal = function () {
            scope.documento = null;
            $mdDialog.cancel();
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('ControlePrazoController', ['$scope', '$mdDialog', 'RecursoService', 'ComumFactory', 'documento', 'filtro', '$timeout', controller]);

    function controller($scope, $mdDialog, service, ComumFactory, documento, filtro, $timeout) {
        var scope = this;

        var PRAZO_DEZ_DIAS = 10;
        var PRAZO_TRINTA_UM_DIAS = 30;
        var PRAZO_SESSENTA_DIAS = 60;

        var APRESENTADO_PRIMEIRA_INSTANCIA = "APRESENTADO_PRIMEIRA_INSTANCIA";
        var DEFERIDO = "DEFERIDO_PRIMEIRA_INSTANCIA";
        var DEFERIDO_PARCIALMENTE = "DEFERIDO_PARCIALMENTE_PRIMEIRA_INSTANCIA";
        var INDEFERIDO = "INDEFERIDO_PRIMEIRA_INSTANCIA";
        var ENVIADO_JURIDICO = "ENVIADO_JURIDICO";
        var APRESENTADO_EM_SEGUNDA_INSTANCIA = "APRESENTADO_EM_SEGUNDA_INSTANCIA";
        var RECURSO_RECONSIDERADO = "RECURSO_RECONSIDERADO";
        var DEFERIDO_SEGUNDA_INSTANCIA = "DEFERIDO_SEGUNDA_INSTANCIA";
        var DEFERIDO_PARCIALMENTE_SEGUNDA_INSTANCIA = "DEFERIDO_PARCIALMENTE_SEGUNDA_INSTANCIA";
        var INDEFERIDO_SEGUNDA_INSTANCIA = "INDEFERIDO_SEGUNDA_INSTANCIA";
        var NAO_APRESENTOU_RECURSO_EM_PRIMEIRA_INSTANCIA = "NAO_APRESENTOU_RECURSO_EM_PRIMEIRA_INSTANCIA";

        scope.init = function () {
            scope.apenasLeitura = true;
            scope.hoje = new Date();
            scope._filtro = filtro;
            scope.documento = angular.copy(documento);

            if(scope.documento.recurso){
                scope.findBy(scope.documento.recurso.id, function(){
                   scope.apenasLeitura = false;
                });

            }else{
                scope.novoRegistro();
            }
        };

        scope.novoRegistro = function(){
            scope._registro = {};
            scope._registro.idDocumento = documento.id;
            scope._registro.primeiraInstancia = false;
            scope._registro.segundaInstancia = false;
            scope._registro.enviouJuridico = false;
            scope._registro.deferido = false;
            scope._registro.deferidoParcialmente = false;
            scope._registro.indeferido = false;
            scope._registro.processoAdminPrimeiraInstancia = null;
            scope._registro.dataRecursoPrimeiraInstancia = null;
            scope._registro.processoAdminPrimeiraInstanciaResultado = null;
            scope._registro.dataEncaminhamento = null;
            scope._registro.dataEntregaRecursoSegundaInstancia = null;
            scope._registro.dataEntregaPrimeiraInstancia = null;
            scope._registro.dataEntregaResulstadoSegundaInstancia = null;
            scope._registro.dataEnvioJuridico = null;
            scope._registro.dataRecursoPrimeiraInstancia = null;
            scope._registro.dataRecursoSegundaInstancia = null;

            scope.showResultado = true;
            scope.showReconsideracao = false;
            scope.disableInstancia = false;
            scope.campoDisable = true;

            scope.apenasLeitura = false;
        };

        scope.persistirRegistro = function(registro){
            scope.apenasLeitura = true;
            if($scope.formRegistro.$valid){
                service.salvar(registro).then(function(){
                    $timeout(function () {
                        scope.filtrar(scope._filtro, function(){
                            scope._registro = {};

                            $scope.formRegistro.$setPristine();
                            $scope.formRegistro.$setUntouched();
                            scope.cancelModal();
                        });
                    });
                });
            }else{
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
                scope.apenasLeitura = false;
            }
        };

        scope.findBy = function(id, callback){
            service.findBy(id).then(function(objectResponse){
                scope._registro = angular.copy(objectResponse.data);

                scope._registro.idDocumento = angular.copy(documento.id);

                scope._registro.dataRecursoPrimeiraInstancia ? scope._registro.dataRecursoPrimeiraInstancia = new Date(scope._registro.dataRecursoPrimeiraInstancia) : scope._registro.dataRecursoPrimeiraInstancia = '';
                scope._registro.dataEnvioJuridico ? scope._registro.dataEnvioJuridico = new Date(scope._registro.dataEnvioJuridico) : scope._registro.dataEnvioJuridico = '';
                scope._registro.dataEntregaPrimeiraInstancia ? scope._registro.dataEntregaPrimeiraInstancia = new Date(scope._registro.dataEntregaPrimeiraInstancia) : scope._registro.dataEntregaPrimeiraInstancia = '';
                scope._registro.dataRecursoSegundaInstancia ? scope._registro.dataRecursoSegundaInstancia = new Date(scope._registro.dataRecursoSegundaInstancia) : scope._registro.dataRecursoSegundaInstancia = '';
                scope._registro.dataEntregaRecursoSegundaInstancia ? scope._registro.dataEntregaRecursoSegundaInstancia = new Date(scope._registro.dataEntregaRecursoSegundaInstancia) : scope._registro.dataEntregaRecursoSegundaInstancia = '';
                scope._registro.dataEncaminhamento ? scope._registro.dataEncaminhamento = new Date(scope._registro.dataEncaminhamento) : scope._registro.dataEncaminhamento = '';
                scope._registro.dataRecursoPrimeiraInstancia ? scope.validarPrazoRecursoSessentaDias(scope._registro.dataRecursoPrimeiraInstancia) : "";
                scope._registro.dataRecursoSegundaInstancia ? scope.validarRecursoDezDias(scope._registro.dataRecursoSegundaInstancia) : "";
                scope._registro.reconsideracao === null ? scope._registro.reconsideracao = false : '';


                scope.populaRadioButtons();
                scope.disableEnvioJuridico();
                scope.disableResultadoPrimeiraInstancia();
                scope.disableCamposSegundaInstancia(scope._registro.segundaInstancia);
                scope.handleSegundaInstaciaSelected(scope._registro.segundaInstancia);

                $timeout(function(){
                    scope._registro.dataEntregaResultadoSegundaInstancia ? scope._registro.dataEntregaResultadoSegundaInstancia = new Date(scope._registro.dataEntregaResultadoSegundaInstancia) : scope._registro.dataEntregaResultadoSegundaInstancia = '';
                });

                scope.apenasLeitura = false;

                if(callback){
                    callback();
                }
            });
        };

        /**
         * Valida o resultado so recurso em primeira instância
         * @param situacao
         */
        scope.handleSituacaoPrimeiraInstancia = function(situacao){
            switch (situacao) {
                case 'deferido':
                    scope._registro.deferidoPrimeiraInstancia = true;
                    scope._registro.deferidoParcialmentePrimeiraInstancia = false;
                    scope._registro.indeferidoPrimeiraInstancia = false;
                    scope._registro.statusSancao = DEFERIDO;
                    break;
                case 'deferidoParcialmente':
                    scope._registro.deferidoPrimeiraInstancia = false;
                    scope._registro.deferidoParcialmentePrimeiraInstancia = true;
                    scope._registro.indeferidoPrimeiraInstancia = false;
                    scope._registro.statusSancao = DEFERIDO_PARCIALMENTE;
                    break;
                case 'indeferido':
                    scope._registro.deferidoPrimeiraInstancia = false;
                    scope._registro.deferidoParcialmentePrimeiraInstancia = false;
                    scope._registro.indeferidoPrimeiraInstancia = true;
                    scope._registro.statusSancao = INDEFERIDO;
                    break;
            }
        };

        /**
         * Valida o resultado so recurso em segunda instância
         * @param situacao
         */
        scope.handleSituacaoSegundaInstancia = function(situacao){
            switch (situacao) {
                case 'deferido':
                    scope._registro.deferidoSegundaInstancia = true;
                    scope._registro.deferidoParcialmenteSegundaInstancia = false;
                    scope._registro.indeferidoSegundaInstancia = false;
                    scope._registro.statusSancao = DEFERIDO_SEGUNDA_INSTANCIA;
                    break;
                case 'deferidoParcialmente':
                    scope._registro.deferidoSegundaInstancia = false;
                    scope._registro.deferidoParcialmenteSegundaInstancia = true;
                    scope._registro.indeferidoSegundaInstancia = false;
                    scope._registro.statusSancao = DEFERIDO_PARCIALMENTE_SEGUNDA_INSTANCIA;
                    break;
                case 'indeferido':
                    scope._registro.deferidoSegundaInstancia = false;
                    scope._registro.deferidoParcialmenteSegundaInstancia = false;
                    scope._registro.indeferidoSegundaInstancia = true;
                    scope._registro.statusSancao = INDEFERIDO_SEGUNDA_INSTANCIA;
                    break;
            }
        };

        scope.populaRadioButtons = function(){
            scope._registro.deferidoPrimeiraInstancia ? scope.situacaoPrimeiraInstancia = "deferido" : "";
            scope._registro.deferidoParcialmentePrimeiraInstancia ? scope.situacaoPrimeiraInstancia = "deferidoParcialmente" : "";
            scope._registro.indeferidoPrimeiraInstancia ? scope.situacaoPrimeiraInstancia = "indeferido" : "";

            scope._registro.deferidoSegundaInstancia ? scope.situacaoSegundaInstancia = "deferido" : "";
            scope._registro.deferidoParcialmenteSegundaInstancia ? scope.situacaoSegundaInstancia = "deferidoParcialmente" : "";
            scope._registro.indeferidoSegundaInstancia ? scope.situacaoSegundaInstancia = "indeferido" : "";
        };

        scope.cancelModal = function () {
            scope._registro = null;
            $mdDialog.cancel();
        };

        /**
         * Verifica se a data atual ultrapassa do prazo estabelecido de 60 dias
         * @param dataRecurso
         */
        scope.validarPrazoRecursoSessentaDias = function(dataRecurso){
            if(dataRecurso){
                var hojeTime = scope.hoje.getTime();
                var prazoMaximo = scope.getPrazo(dataRecurso, PRAZO_SESSENTA_DIAS);

                hojeTime > prazoMaximo ? scope.showResultado = false : scope.showResultado = true;
            }
        };

        /**
         * Verifica se a data atual ultrapassa do prazo estabelecido de 30 dias
         */
        scope.validarRecursoTrintaUmDidas = function(){
            var hojeTime = scope.hoje.getTime();
            var prazoMaximo = scope.getPrazo(scope.documento.dataEntrega, PRAZO_TRINTA_UM_DIAS);

            hojeTime > prazoMaximo ? scope._registro.primeiraInstancia = false : "";
            prazoMaximo > hojeTime ? scope._registro.statusSancao = NAO_APRESENTOU_RECURSO_EM_SEGUNDA_INSTANCIA : "";

            scope.disableInstancia = true;
        };

        /**
         * Verifica se a data atual ultrapassa o prazo estabelecido de 10 dias
         */
        scope.validarRecursoDezDias = function(dataRecurso){
            if(dataRecurso){
                var hojeTime = scope.hoje.getTime();
                var prazoMaximo = scope.getPrazo(dataRecurso, PRAZO_DEZ_DIAS);

                scope.disableCamposSegundaInstancia();

                hojeTime > prazoMaximo ? scope.showReconsideracao = false : scope.showReconsideracao = true;
                hojeTime > prazoMaximo ? scope._registro.statusSancao = NAO_APRESENTOU_RECURSO_EM_PRIMEIRA_INSTANCIA : "";
            }
        };

        /**
         * Obtém a data do recuso e acrescenta em dias o valor do vencimento
         * @param dataRecurso
         * @param vencimento
         * @returns {number}
         */
        scope.getPrazo = function(dataRecurso, vencimento){
            var dia = angular.copy(dataRecurso.getDate());
            var mes = angular.copy(dataRecurso.getMonth());
            var ano = angular.copy(dataRecurso.getFullYear());
            var prazo = new Date(ano, mes, dia + vencimento);

            return prazo.getTime();
        };

        scope.disableEnvioJuridico = function(){
            if(scope.apenasLeitura || !scope._registro.enviouJuridico || !scope._registro.id){
                return true;
            }else{
                return false;
            }
        };

        scope.disableResultadoPrimeiraInstancia = function(){
            if(scope.apenasLeitura || !scope.situacaoPrimeiraInstancia || !scope._registro.id){
                return true;
            }else{
                return false;
            }
        };

        scope.disableCamposSegundaInstancia = function(){
            switch (scope.documento.status) {
                case 'APRESENTADO_EM_SEGUNDA_INSTANCIA':
                    scope.campoDisable = false;
                    break;
                case 'RECURSO_RECONSIDERADO':
                    scope.campoDisable = false;
                    break;
                case 'DEFERIDO_SEGUNDA_INSTANCIA':
                    scope.campoDisable = false;
                    break;
                case 'DEFERIDO_PARCIALMENTE_SEGUNDA_INSTANCIA':
                    scope.campoDisable = false;
                    break;
                case 'INDEFERIDO_SEGUNDA_INSTANCIA':
                    scope.campoDisable = false;
                    break;
            }
        };

        /**
         * Verifica se a instanca foi apresentada em primeira instancia e muda o valor do status do documento
         * @param value
         */
        scope.handlePrimeiraInstanciaSelected = function(value){
            value === true ? scope._registro.statusSancao = APRESENTADO_PRIMEIRA_INSTANCIA : scope._registro.statusSancao = null;
        };

        /**
         * Verifica se o documento foi enviado para o juridio e muda o valor do status do documento
         * @param value
         */
        scope.handleJuridicoSelected = function(value){
            value === true ? scope._registro.statusSancao = ENVIADO_JURIDICO : scope._registro.statusSancao = null;
        };

        /**
         * Verifica se o documento foi apresentado em segunda instancia e muda o valor do status do documento
         * @param value
         */
        scope.handleSegundaInstaciaSelected = function(value){
          value === true ? scope._registro.statusSancao = APRESENTADO_EM_SEGUNDA_INSTANCIA : scope._registro.statusDocumeno = null;
        };

        /**
         * verifica se houve reconsideração do recurso e muda o status do documento
         * @param value
         */
        scope.handleReconsideracaoSelected = function(value){
            if(value === true){
                scope._registro.statusSancao = RECURSO_RECONSIDERADO;
                scope.situacaoSegundaInstancia = null;
                scope._registro.dataEntregaResultadoSegundaInstancia = null;
                scope._registro.processoAdminSegundaInstanciaResultado = null;
            }else{
                scope._registro.statusDocumeno = null;
                scope._registro.dataEntregaRecursoSegundaInstancia = null;
                scope._registro.processoAdminReconsideracao = null;
                scope.situacaoSegundaInstancia = null;
            }
        };

        scope.filtrar = function (filtro, callback) {
            filtro.page = scope.registroInicial - 1;
            filtro.size = scope.tamanhoLimite;

            if (filtro.orderBy === 'ASC') {
                filtro.orderBy = 'DESC';
                filtro.order = filtro.order.replace('-', '');
            } else {
                filtro.orderBy = 'ASC';
                filtro.order = filtro.order.replace('-', '');
            }

            service.findDocumentosByFilter(filtro).then(function (objectReturn) {
                scope._registros = objectReturn.data.content || [];
                scope.totalItems = objectReturn.data.totalElements;

                if(callback){
                    callback();
                }
            });

            if (filtro.orderBy === 'DESC') {
                filtro.order = '-' + filtro.order;
            }
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('NovaDemandaController', ['$state', '$scope', '$rootScope', '$mdDialog', 'RelatorioService', 'ordemFiscalizacao', 'apenasLeitura', '$window', 'ComumFactory', controller]);

    function controller($state, $scope, $rootScope, $mdDialog, service, ordemFiscalizacao, apenasLeitura, $window, ComumFactory) {
        var scope = this;

        /**
         * Inicia o cadastro da criação de uma nova demanda a partir do relatório de fiscalização
         */
        scope.init = function(){
          scope.ordemFiscalizacao = angular.copy(ordemFiscalizacao);
          scope.apenasLeitura = angular.copy(apenasLeitura);
        };

        /**
         * Fecha a modal de justificativa de uma nova demanda
         */
        scope.cancelModalNovaDemanda = function (callback) {
            $mdDialog.cancel();

            if(callback){
                callback();
            }
        };

        scope.salvar = function (justificativaNovaDemanda) {
            scope.apenasLeitura = true;

            if($scope.formJustificativaNovaDemanda.$valid){
                $window.localStorage.setItem("justificativaNovaDemanda", justificativaNovaDemanda);

                scope.cancelModalNovaDemanda(function(){
                    scope.justificativaNovaDemanda = null;
                    $state.go("fiscalizacao.demanda.demandaOf", {"ofId": scope.ordemFiscalizacao.id});
                });

                scope.cancelModalNovaDemanda();
            }else{
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
                scope.apenasLeitura = false;
            }
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('RelatorioListarController', ['$mdDialog', 'RelatorioService', 'ComumFactory', '$state', 'IntegradorService', '$scope', 'ciMapaLayersFactoryFiscalizacao', 'ciMapaViewFactoryFiscalizacao', '$timeout', '$stateParams', '$q', 'ArquivoServiceFiscalizacao', controller]);

    function controller($mdDialog, service, ComumFactory, $state, integradorService, $scope, ciMapaLayersFactoryFiscalizacao, ciMapaViewFactoryFiscalizacao, $timeout, $stateParams, $q, ArquivoServiceFiscalizacao) {
        var scope = this;
        scope.registroInicial = 1;
        scope.page = 1;
        scope.tamanhoLimite = 10;
        scope.optionTamanho = [10, 20, 50];
        scope.maxRegistros = 10;
        scope.totalItems = 0;

        scope.init = function () {

            service.getStatus().then(function (objectReturn) {
                scope._status = objectReturn.data;
            });

            scope.limparFiltros();
        };

        scope.limparFiltros = function () {
            scope.filtros = {
                order: 'dataEdicao',
                orderBy: 'ASC',
                inicio: '',
                fim: ''
            };
            scope.filtrar();
        };


        scope.filtrar = function () {
            scope.filtros.ordemFiscalizacao = {};
            scope.filtros.ordemFiscalizacao.protocolo = scope.filtros.descricao;
            scope.filtros.responsavel = {};
            scope.filtros.responsavel.cpfCnpj = scope.filtros.responsavel.cpfCnpj;
            scope.filtros.page = scope.registroInicial - 1;
            scope.filtros.size = scope.tamanhoLimite;

            if (scope.filtros.orderBy === 'ASC') {
                scope.filtros.orderBy = 'DESC';
                scope.filtros.order = scope.filtros.order.replace('-', '');
            } else {
                scope.filtros.orderBy = 'ASC';
                scope.filtros.order = scope.filtros.order.replace('-', '');
            }

            service.findByFilter(scope.filtros).then(function (retorno) {
                scope._registros = retorno.data.content || [];
                scope.totalItems = retorno.data.totalElements;
            });
        };

        scope.getDescricaoBySearch = function () {
            if (scope.filtros.descricao && scope.filtros.descricao.length >= 2) {
                service.getDescricaoBySearch(scope.filtros.descricao).then(function (objectReturn) {
                    $('#search').find('input').blur();
                    $timeout(function () {
                        $('#search').find('input').focus();
                    });
                    scope.descricoes = objectReturn.data;
                    return objectReturn.data;
                });
            } else {
                scope.descricoes = [];
                var deferred = $q.defer();
                deferred.resolve([]);
                return deferred.promise;
            }
        };

        scope.getDemandasByProtocolo = function (protocolo) {
            if (protocolo) {
                scope.filtros.descricao = protocolo;
                service.getDemandasByProtocolo(protocolo).then(function (objectResponse) {
                    scope._demandas = objectResponse.data;
                });
            } else {
                scope.filtros.demanda = null;
            }
        };

        scope.excluirRegistro = function (registro) {
            var confirm = $mdDialog.confirm()
                .title('Atenção!')
                .textContent('Tem certeza que deseja cancelar este Relatório de Fiscalização?')
                .ok('Confirmar')
                .cancel('Cancelar');

            $mdDialog.show(confirm).then(function () {
                service.deleteRelatorio(registro).then(function () {
                    scope.filtrar();
                });
            });
        };


        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('JustificativaDocumentoController', ['$scope', '$mdDialog', 'RelatorioInfracaoService', 'ComumFactory', 'documento', 'relatorioFiscalizacao', 'autuado', 'minDataFiscalizacao', 'tipoJustificativa', 'RelatorioService', '$state', controller]);

    function controller($scope, $mdDialog, RelatorioInfracaoService, ComumFactory, documento, relatorioFiscalizacao, autuado, minDataFiscalizacao, tipoJustificativa, RelatorioService, $state) {
        var scope = this;

        scope.init = function(){
            scope.documento = documento;
            scope.relatorioFiscalizacao = relatorioFiscalizacao;
            scope.autuado = autuado;
            scope.tipoJustificativa = tipoJustificativa;

            scope.tipoJustificativa === 'edicao' ? scope.tipo = 'edição' : scope.tipo = 'exclusão';
            scope.tipoJustificativa === 'edicao' ? scope.isEdicao = true : scope.isEdicao = false;
        };

        /**
         * Fecha a modal de auto de infração
         */
        scope.cancelModalJustificativaDocumento = function (callback) {
            $mdDialog.cancel();

            if(callback){
                callback();
            }
        };

        scope.salvar = function(justificativa){
            scope.apenasLeitura = true;

            if($scope.formJustificativaDocumento.$valid){

                scope.isEdicao ? scope.documento.justificativaEdicao = angular.copy(justificativa) : scope.documento.justificativaExclusao = angular.copy(justificativa);

                if(scope.documento.id && !scope.isEdicao){
                    scope.documento.apenasLeitura = true;
                    RelatorioService.excluirDocumento(scope.documento).then(function(){
                        RelatorioInfracaoService.openModal(event, scope.documento, scope.relatorioFiscalizacao, scope.autuado.responsavel, scope.relatorioFiscalizacao.inicio, scope);
                        scope.cancelModalJustificativaDocumento();

                        scope.justificativa = null;
                        $scope.formJustificativaDocumento.$setPristine();
                        $scope.formJustificativaDocumento.$setUntouched();

                        $state.go('fiscalizacao.relatorio.listar');
                    });
                }else{
                    scope.cancelModalJustificativaDocumento();
                    RelatorioInfracaoService.openModal(event, scope.documento, scope.relatorioFiscalizacao, scope.autuado.responsavel, scope.relatorioFiscalizacao.inicio, scope);

                    scope.justificativa = null;
                    $scope.formJustificativaDocumento.$setPristine();
                    $scope.formJustificativaDocumento.$setUntouched();

                    $state.go('fiscalizacao.relatorio.listar');
                }
            }else{
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
                scope.apenasLeitura = false;
            }
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('RelatorioExportarController', ['$filter', '$scope', '$rootScope', '$state', '$stateParams', '$timeout', 'RelatorioService', 'ComumFactory', 'IntegradorService', controller]);

    function controller($filter, $scope, $rootScope, $state, $stateParams, $timeout, service, ComumFactory, IntegradorService) {
        var scope = this;

        scope.init = function () {
            scope.getUsuario(function () {
                scope.getEstados(function () {
                    scope.iniciarFiltros(function(){});
                });
            });
        };

        scope.getEstados = function (callback) {
            scope._estados = [];
            IntegradorService.getEstados().then(function (objectReturn) {
                objectReturn.data.forEach(function (estado) {
                    scope._usuario.jurisdicoes.forEach(function (juridicao) {
                        if (juridicao === estado.sigla) {
                            scope._estados.push(estado);
                        }
                    });
                });    

      
                if (callback) {
                    callback();
                }
            });
        };

        scope.getUsuario = function (callback) {
            scope._usuario = {};
            service.getUsuario().then(function (objectReturn) {
                scope._usuario = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.selecionaTodosEstados = function (){
            scope._filtros.uf = [];
 
            scope._estados.forEach(function (estado) {
                scope._filtros.uf.push(estado.sigla);
            });
        };

        
        scope.exportarCsv = function(filtros){
            service.exportarCsv(filtros).then(function(resp){
                
                var dataAtual = new Date();
 
                var ano = dataAtual.getFullYear().toString();
                var mes = dataAtual.getMonth() + 1
                var dia = dataAtual.getDate().toString();

                var csvFile = new Blob([resp.data], { type: "text/csv; charset=UTF-8" });
                var downloadLink = document.createElement("a");
                downloadLink.download = 'Exportação_Fiscalização_CSV_'+ano+"_"+mes.toString()+"_"+dia+"_.csv";
                downloadLink.href = window.URL.createObjectURL(csvFile);
                downloadLink.style.display = "none";
                document.body.appendChild(downloadLink);
                downloadLink.click();
            });
        };


        scope.iniciarFiltros = function (callback) {
            
            if ($scope.formRegistro) {
                $scope.formRegistro.$setUntouched();
                $scope.formRegistro.$setPristine();
            }
           
            var d = new Date();
            var ano = d.getFullYear();
            var dataInicial = new Date('01/01/' + ano);
            var dataFinal = new Date('12/31/' + ano);

            scope._filtros = {};
            scope._filtros.dataInicio = dataInicial;
            scope._filtros.dataFim = dataFinal;
            scope._filtros.uf = [];

            // dados do cadastrador
            scope._filtros.dataHora = true;
            scope._filtros.cpfCadastrador = true;
            scope._filtros.nomeCadastrador = true;

             // dados da demanda
             scope._filtros.nuProcessoDemanda = true;
             scope._filtros.protocoloOrdemFiscalizacao = true;
             scope._filtros.dataFiscalizacao = true;
             scope._filtros.motivacaoDemanda = true;
             scope._filtros.cnpjCpfResponsavelDemanda = true;
             scope._filtros.nomeResponsavelDemanda = true;
             scope._filtros.ufDemanda = true;
             scope._filtros.areaImovelDemanda = true;
             scope._filtros.cepDemanda = true;
             scope._filtros.destinacaoDemanda = true;
             scope._filtros.municipioDemanda = true;
             scope._filtros.bairroDemanda = true;
             scope._filtros.tipoLogradouroDemanda = true;
             scope._filtros.logradouroDemanda = true;
             scope._filtros.numeroEnderecoDemanda = true;
             scope._filtros.complementoDemanda = true;
             scope._filtros.naturezaImovelDemanda = true;

             // dados do relatorio
             scope._filtros.benfeitoriasRelatorio = true;
             scope._filtros.confirmaDadosOcupanteRelatorio = true;
             scope._filtros.cnpjCpfResponsavelRelatorio = true;
             scope._filtros.nomeResponsavelRelatorio = true;
             scope._filtros.confirmaEnderecoRelatorio = true;
             scope._filtros.ufRelatorio = true;
             scope._filtros.areaImovelRelatorio = true;
             scope._filtros.cepRelatorio = true;
             scope._filtros.municipioRelatorio = true;
             scope._filtros.bairroRelatorio = true;
             scope._filtros.tipoLogradouroRelatorio = true;
             scope._filtros.logradouroRelatorio = true;
             scope._filtros.numeroEnderecoRelatorio = true;
             scope._filtros.complementoRelatorio = true;
             scope._filtros.naturezaImovelRelatorio = true;
             scope._filtros.descritivoRelatorio = true;
             scope._filtros.equipamentoRPARelatorio = true;

             // dados croqui
             scope._filtros.latitudeCroqui = true;
             scope._filtros.longitudeCroqui = true;
             scope._filtros.encaminhamentosCroqui = true;
             scope._filtros.membrosEquipeCroqui = true;
             scope._filtros.infracaoCroqui = true;

             //dados autuado
             scope._filtros.cpfCnpjAutuado = true;
             scope._filtros.nomeAutuado = true;
             scope._filtros.processosSEIAutuado = true;
             
             //dados Auto de embargo
             scope._filtros.uploadArquivoAutoEmbargo = true;
             scope._filtros.dataAutuacaoAutoEmbargo = true;
             scope._filtros.horaAutuacaoAutoEmbargo = true;
             scope._filtros.areaEmbargoAutoEmbargo = true;
             scope._filtros.cnpjCpfResponsavelAutoEmbargo = true;
             scope._filtros.nomeResponsavelAutoEmbargo = true;
             scope._filtros.documentoIdResponsavelAutoEmbargo = true;

            //dados RG
             scope._filtros.numeroRGAutoEmbargo = true;
             scope._filtros.orgaoEmissorRGAutoEmbargo = true;
             scope._filtros.ufRGAutoEmbargo = true;

            //dados Passaporte
             scope._filtros.numeroPassaporteAutoEmbargo = true;
             scope._filtros.paisEmissorPassaporteAutoEmbargo = true;
             scope._filtros.filiacaoPassaporteAutoEmbargo = true;
             scope._filtros.dataNascimentoPassaporteAutoEmbargo = true;
             scope._filtros.nacionalidadePassaporteAutoEmbargo = true;
             scope._filtros.naturalidadePassaporteAutoEmbargo = true;
             scope._filtros.correspondenciaDiferentePassaporteAutoEmbargo = true;
             scope._filtros.telefonePassaporteAutoEmbargo = true;
             scope._filtros.emailPassaporteAutoEmbargo = true;
             scope._filtros.descricaoFatoPassaporteAutoEmbargo = true;

               //dados Auto de infracao
               scope._filtros.uploadArquivoAutoInfracao = true;
               scope._filtros.dataAutuacaoAutoInfracao = true;
               scope._filtros.horaAutuacaoAutoInfracao = true;
               scope._filtros.areaEmbargoAutoInfracao = true;
               scope._filtros.cnpjCpfResponsavelAutoInfracao = true;
               scope._filtros.nomeResponsavelAutoInfracao = true;
               scope._filtros.rgResponsavelAutoInfracao = true;
               scope._filtros.numeroRgResponsavelAutoInfracao = true;
               scope._filtros.orgaoEmissorRgResponsavelAutoInfracao = true;
               scope._filtros.ufEmissaoRgResponsavelAutoInfracao = true;
               scope._filtros.passaporteAutoInfracao = true;
               scope._filtros.numeroPassaporteAutoInfracao = true;
               scope._filtros.paisEmissorPassaporteAutoInfracao = true;
               scope._filtros.filiacaoPassaporteAutoInfracao = true;
               scope._filtros.dataNascimentoPassaporteAutoInfracao = true;
               scope._filtros.nacionalidadePassaporteAutoInfracao = true;
               scope._filtros.naturalidadePassaporteAutoInfracao = true;
               scope._filtros.correspondenciaDiferentePassaporteAutoInfracao = true;
               scope._filtros.telefonePassaporteAutoInfracao = true;
               scope._filtros.emailPassaporteAutoInfracao = true;
               scope._filtros.descricaoFatoPassaporteAutoInfracao = true;

            //dados sancao
             scope._filtros.embargoObraSancao = true;
             scope._filtros.demolicaoSancao = true;
             scope._filtros.multaSancao = true;
             scope._filtros.areaSancao = true;
             scope._filtros.valorMetroSancao = true;
             scope._filtros.valorTotalMultaSancao = true;
             scope._filtros.indenizacaoSancao = true;

             scope._filtros.anoInicialOcupacaoSancao = true;
             scope._filtros.anoFinalOcupacaoSancao = true;
             scope._filtros.areaOcupacaoSancao = true;

             scope._filtros.anoInicialCobrancaSancao = true;
             scope._filtros.anoFinalCobrancaSancao = true;

             //dados notificacao
             scope._filtros.tipoNotificacao = true;
             scope._filtros.uploadArquivoNotificacao = true;
             scope._filtros.dataAutuacaoNotificacao = true;
             scope._filtros.horaAutuacaoNotificacao = true;
             scope._filtros.areaEmbargoNotificacao = true;
             scope._filtros.cnpjCpfResponsavelNotificacao = true;
             scope._filtros.nomeResponsavelNotificacao = true;
             scope._filtros.rgResponsavelNotificacao = true;
             scope._filtros.numeroRgResponsavelNotificacao = true;
             scope._filtros.orgaoEmissorRgResponsavelNotificacao = true;
             scope._filtros.ufEmissaoRgResponsavelNotificacao = true;
             scope._filtros.passaporteNotificacao = true;
             scope._filtros.numeroPassaporteNotificacao = true;
             scope._filtros.paisEmissorPassaporteNotificacao = true;
             scope._filtros.filiacaoPassaporteNotificacao = true;
             scope._filtros.dataNascimentoPassaporteNotificacao = true;
             scope._filtros.nacionalidadePassaporteNotificacao = true;
             scope._filtros.naturalidadePassaporteNotificacao = true;
             scope._filtros.correspondenciaDiferentePassaporteNotificacao = true;
             scope._filtros.telefonePassaporteNotificacao = true;
             scope._filtros.emailPassaporteNotificacao = true;
             scope._filtros.descricaoFatoPassaporteNotificacao = true;


            if (callback) {
                callback();
            }

        };

        scope.habilitaTodosDadosCadastrador = function(){
            scope._filtros.dataHora = !scope._filtros.dataHora;
            scope._filtros.cpfCadastrador = !scope._filtros.cpfCadastrador;
            scope._filtros.nomeCadastrador = !scope._filtros.nomeCadastrador;
        };

        scope.habilitaTodosDadosDemanda = function(){
            scope._filtros.nuProcessoDemanda = !scope._filtros.nuProcessoDemanda;
            scope._filtros.protocoloOrdemFiscalizacao = !scope._filtros.protocoloOrdemFiscalizacao;
            scope._filtros.dataFiscalizacao = !scope._filtros.dataFiscalizacao;
            scope._filtros.motivacaoDemanda = !scope._filtros.motivacaoDemanda;
            scope._filtros.cnpjCpfResponsavelDemanda = !scope._filtros.cnpjCpfResponsavelDemanda;
            scope._filtros.nomeResponsavelDemanda = !scope._filtros.nomeResponsavelDemanda;
            scope._filtros.ufDemanda = !scope._filtros.ufDemanda;
            scope._filtros.areaImovelDemanda = !scope._filtros.areaImovelDemanda;
            scope._filtros.cepDemanda = !scope._filtros.cepDemanda;
            scope._filtros.destinacaoDemanda = !scope._filtros.destinacaoDemanda;
            scope._filtros.municipioDemanda = !scope._filtros.municipioDemanda;
            scope._filtros.bairroDemanda = !scope._filtros.bairroDemanda;
            scope._filtros.tipoLogradouroDemanda = !scope._filtros.tipoLogradouroDemanda;
            scope._filtros.logradouroDemanda = !scope._filtros.logradouroDemanda;
            scope._filtros.numeroEnderecoDemanda = !scope._filtros.numeroEnderecoDemanda;
            scope._filtros.complementoDemanda = !scope._filtros.complementoDemanda;
            scope._filtros.naturezaImovelDemanda = !scope._filtros.naturezaImovelDemanda;
        };

        scope.habilitaTodosDadosRelatorio = function(){
            scope._filtros.benfeitoriasRelatorio = !scope._filtros.benfeitoriasRelatorio;
            scope._filtros.confirmaDadosOcupanteRelatorio = !scope._filtros.confirmaDadosOcupanteRelatorio;
            scope._filtros.cnpjCpfResponsavelRelatorio = !scope._filtros.cnpjCpfResponsavelRelatorio;
            scope._filtros.nomeResponsavelRelatorio = !scope._filtros.nomeResponsavelRelatorio;
            scope._filtros.confirmaEnderecoRelatorio = !scope._filtros.confirmaEnderecoRelatorio;
            scope._filtros.ufRelatorio = !scope._filtros.ufRelatorio;
            scope._filtros.areaImovelRelatorio = !scope._filtros.areaImovelRelatorio;
            scope._filtros.cepRelatorio = !scope._filtros.cepRelatorio;
            scope._filtros.municipioRelatorio = !scope._filtros.municipioRelatorio;
            scope._filtros.bairroRelatorio = !scope._filtros.bairroRelatorio;
            scope._filtros.tipoLogradouroRelatorio = !scope._filtros.tipoLogradouroRelatorio;
            scope._filtros.logradouroRelatorio = !scope._filtros.logradouroRelatorio;
            scope._filtros.numeroEnderecoRelatorio = !scope._filtros.numeroEnderecoRelatorio;
            scope._filtros.complementoRelatorio = !scope._filtros.complementoRelatorio;
            scope._filtros.naturezaImovelRelatorio = !scope._filtros.naturezaImovelRelatorio;
            scope._filtros.descritivoRelatorio = !scope._filtros.descritivoRelatorio;
            scope._filtros.equipamentoRPARelatorio = !scope._filtros.equipamentoRPARelatorio;
        }

        scope.habilitaTodosDadosCroqui = function(){
            scope._filtros.latitudeCroqui = !scope._filtros.latitudeCroqui;
            scope._filtros.longitudeCroqui = !scope._filtros.longitudeCroqui;
            scope._filtros.encaminhamentosCroqui = !scope._filtros.encaminhamentosCroqui;
            scope._filtros.membrosEquipeCroqui = !scope._filtros.membrosEquipeCroqui;
            scope._filtros.infracaoCroqui = !scope._filtros.infracaoCroqui;
        }

        
        scope.habilitaTodosDadosAutuado = function(){
            scope._filtros.cpfCnpjAutuado = !scope._filtros.cpfCnpjAutuado;
            scope._filtros.nomeAutuado = !scope._filtros.nomeAutuado;
            scope._filtros.processosSEIAutuado = !scope._filtros.processosSEIAutuado;
        };

        scope.habilitaTodosDadosAutoEmbargo = function(){
            scope._filtros.uploadArquivoAutoEmbargo = !scope._filtros.uploadArquivoAutoEmbargo;
            scope._filtros.dataAutuacaoAutoEmbargo = !scope._filtros.dataAutuacaoAutoEmbargo;
            scope._filtros.horaAutuacaoAutoEmbargo = !scope._filtros.horaAutuacaoAutoEmbargo;
            scope._filtros.areaEmbargoAutoEmbargo = !scope._filtros.areaEmbargoAutoEmbargo;
            scope._filtros.cnpjCpfResponsavelAutoEmbargo = !scope._filtros.cnpjCpfResponsavelAutoEmbargo;
            scope._filtros.nomeResponsavelAutoEmbargo = !scope._filtros.nomeResponsavelAutoEmbargo;
            scope._filtros.documentoIdResponsavelAutoEmbargo = !scope._filtros.documentoIdResponsavelAutoEmbargo;
        };

        scope.habilitaTodosDadosRG = function(){
            scope._filtros.numeroRGAutoEmbargo = !scope._filtros.numeroRGAutoEmbargo;
            scope._filtros.orgaoEmissorRGAutoEmbargo = !scope._filtros.orgaoEmissorRGAutoEmbargo;
            scope._filtros.ufRGAutoEmbargo = !scope._filtros.ufRGAutoEmbargo;
        };

        scope.habilitaTodosDadosPassaporte = function(){
            scope._filtros.numeroPassaporteAutoEmbargo = !scope._filtros.numeroPassaporteAutoEmbargo;
            scope._filtros.paisEmissorPassaporteAutoEmbargo = !scope._filtros.paisEmissorPassaporteAutoEmbargo;
            scope._filtros.filiacaoPassaporteAutoEmbargo = !scope._filtros.filiacaoPassaporteAutoEmbargo;
            scope._filtros.dataNascimentoPassaporteAutoEmbargo = !scope._filtros.dataNascimentoPassaporteAutoEmbargo;
            scope._filtros.nacionalidadePassaporteAutoEmbargo = !scope._filtros.nacionalidadePassaporteAutoEmbargo;
            scope._filtros.naturalidadePassaporteAutoEmbargo = !scope._filtros.naturalidadePassaporteAutoEmbargo;
            scope._filtros.correspondenciaDiferentePassaporteAutoEmbargo = !scope._filtros.correspondenciaDiferentePassaporteAutoEmbargo;
            scope._filtros.telefonePassaporteAutoEmbargo = !scope._filtros.telefonePassaporteAutoEmbargo;
            scope._filtros.emailPassaporteAutoEmbargo = !scope._filtros.emailPassaporteAutoEmbargo;
            scope._filtros.descricaoFatoPassaporteAutoEmbargo = !scope._filtros.descricaoFatoPassaporteAutoEmbargo;
        };


        scope.habilitaTodosDadosAutoInfracao = function(){
            scope._filtros.uploadArquivoAutoInfracao = !scope._filtros.uploadArquivoAutoInfracao;
            scope._filtros.dataAutuacaoAutoInfracao = !scope._filtros.dataAutuacaoAutoInfracao;
            scope._filtros.horaAutuacaoAutoInfracao = !scope._filtros.horaAutuacaoAutoInfracao;
            scope._filtros.areaEmbargoAutoInfracao = !scope._filtros.areaEmbargoAutoInfracao;
            scope._filtros.cnpjCpfResponsavelAutoInfracao = !scope._filtros.cnpjCpfResponsavelAutoInfracao;
            scope._filtros.nomeResponsavelAutoInfracao = !scope._filtros.nomeResponsavelAutoInfracao;
            scope._filtros.rgResponsavelAutoInfracao = !scope._filtros.rgResponsavelAutoInfracao;
            scope._filtros.numeroRgResponsavelAutoInfracao = !scope._filtros.numeroRgResponsavelAutoInfracao;
            scope._filtros.orgaoEmissorRgResponsavelAutoInfracao = !scope._filtros.orgaoEmissorRgResponsavelAutoInfracao;
            scope._filtros.ufEmissaoRgResponsavelAutoInfracao = !scope._filtros.ufEmissaoRgResponsavelAutoInfracao;
            scope._filtros.passaporteAutoInfracao = !scope._filtros.passaporteAutoInfracao;
            scope._filtros.numeroPassaporteAutoInfracao = !scope._filtros.numeroPassaporteAutoInfracao;
            scope._filtros.paisEmissorPassaporteAutoInfracao = !scope._filtros.paisEmissorPassaporteAutoInfracao;
            scope._filtros.filiacaoPassaporteAutoInfracao = !scope._filtros.filiacaoPassaporteAutoInfracao;
            scope._filtros.dataNascimentoPassaporteAutoInfracao = !scope._filtros.dataNascimentoPassaporteAutoInfracao;
            scope._filtros.nacionalidadePassaporteAutoInfracao = !scope._filtros.nacionalidadePassaporteAutoInfracao;
            scope._filtros.naturalidadePassaporteAutoInfracao = !scope._filtros.naturalidadePassaporteAutoInfracao;
            scope._filtros.correspondenciaDiferentePassaporteAutoInfracao = !scope._filtros.correspondenciaDiferentePassaporteAutoInfracao;
            scope._filtros.telefonePassaporteAutoInfracao = !scope._filtros.telefonePassaporteAutoInfracao;
            scope._filtros.emailPassaporteAutoInfracao = !scope._filtros.emailPassaporteAutoInfracao;
            scope._filtros.descricaoFatoPassaporteAutoInfracao = !scope._filtros.descricaoFatoPassaporteAutoInfracao;
        };

        scope.habilitaTodosDadosSancao = function(){
            scope._filtros.embargoObraSancao = !scope._filtros.embargoObraSancao;
            scope._filtros.demolicaoSancao = !scope._filtros.demolicaoSancao;
            scope._filtros.multaSancao = !scope._filtros.multaSancao;
            scope._filtros.areaSancao = !scope._filtros.areaSancao;
            scope._filtros.valorMetroSancao = !scope._filtros.valorMetroSancao;
            scope._filtros.valorTotalMultaSancao = !scope._filtros.valorTotalMultaSancao;
            scope._filtros.indenizacaoSancao = !scope._filtros.indenizacaoSancao;
            
        };

        scope.habilitaTodosDadosPeriodoOcupacaoSancao = function(){
            scope._filtros.anoInicialOcupacaoSancao = !scope._filtros.anoInicialOcupacaoSancao;
            scope._filtros.anoFinalOcupacaoSancao = !scope._filtros.anoFinalOcupacaoSancao;
            scope._filtros.areaOcupacaoSancao = !scope._filtros.areaOcupacaoSancao;           
        };

        scope.habilitaTodosDadosPeriodoCobrancaSancao = function(){
            scope._filtros.anoInicialCobrancaSancao = !scope._filtros.anoInicialCobrancaSancao;
            scope._filtros.anoFinalCobrancaSancao = !scope._filtros.anoFinalCobrancaSancao;
            scope._filtros.areaCobrancaSancao = !scope._filtros.areaCobrancaSancao;           
        };

        
        scope.habilitaTodosDadosNotificacao = function(){
            scope._filtros.tipoNotificacao = !scope._filtros.tipoNotificacao;
            scope._filtros.uploadArquivoNotificacao = !scope._filtros.uploadArquivoNotificacao;
            scope._filtros.dataAutuacaoNotificacao = !scope._filtros.dataAutuacaoNotificacao;
            scope._filtros.horaAutuacaoNotificacao = !scope._filtros.horaAutuacaoNotificacao;
            scope._filtros.areaEmbargoNotificacao = !scope._filtros.areaEmbargoNotificacao;
            scope._filtros.cnpjCpfResponsavelNotificacao = !scope._filtros.cnpjCpfResponsavelNotificacao;
            scope._filtros.nomeResponsavelNotificacao = !scope._filtros.nomeResponsavelNotificacao;
            scope._filtros.rgResponsavelNotificacao = !scope._filtros.rgResponsavelNotificacao;
            scope._filtros.numeroRgResponsavelNotificacao = !scope._filtros.numeroRgResponsavelNotificacao;
            scope._filtros.orgaoEmissorRgResponsavelNotificacao = !scope._filtros.orgaoEmissorRgResponsavelNotificacao;
            scope._filtros.ufEmissaoRgResponsavelNotificacao = !scope._filtros.ufEmissaoRgResponsavelNotificacao;
            scope._filtros.passaporteNotificacao = !scope._filtros.passaporteNotificacao;
            scope._filtros.numeroPassaporteNotificacao = !scope._filtros.numeroPassaporteNotificacao;
            scope._filtros.paisEmissorPassaporteNotificacao = !scope._filtros.paisEmissorPassaporteNotificacao;
            scope._filtros.filiacaoPassaporteNotificacao = !scope._filtros.filiacaoPassaporteNotificacao;
            scope._filtros.dataNascimentoPassaporteNotificacao = !scope._filtros.dataNascimentoPassaporteNotificacao;
            scope._filtros.nacionalidadePassaporteNotificacao = !scope._filtros.nacionalidadePassaporteNotificacao;
            scope._filtros.naturalidadePassaporteNotificacao = !scope._filtros.naturalidadePassaporteNotificacao;
            scope._filtros.correspondenciaDiferentePassaporteNotificacao = !scope._filtros.correspondenciaDiferentePassaporteNotificacao;
            scope._filtros.telefonePassaporteNotificacao = !scope._filtros.telefonePassaporteNotificacao;
            scope._filtros.emailPassaporteNotificacao = !scope._filtros.emailPassaporteNotificacao;
            scope._filtros.descricaoFatoPassaporteNotificacao = !scope._filtros.descricaoFatoPassaporteNotificacao;
        };


        scope.init();
    }
})();
(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('RelatorioDocumentoController', ['$scope', '$mdDialog', 'RelatorioService', 'ComumFactory', '$state', 'IntegradorService', '$timeout', 'documento', 'relatorioFiscalizacao', 'autuado', 'minDataFiscalizacao', controller]);

    function controller($scope, $mdDialog, service, ComumFactory, $state, integradorService, $timeout, documento, relatorioFiscalizacao, autuado, minDataFiscalizacao) {
        var scope = this;
        scope.cepPesquisado = false;

        scope.initDoc = function () {
            scope.minDataFiscalizacao = new Date(minDataFiscalizacao);

            scope.documento = documento;
            scope.relatorio = relatorioFiscalizacao;
            scope.autuado = autuado;

            scope.documento.id ? scope.isEdicao = true : scope.isEdicao = false;

            scope.iniciaCombos(function () {
                scope.carregarRegistroDocumento(function(){
                    scope.apenasLeitura = scope.documento.apenasLeitura;
                });
            });
        };

        scope.iniciaCombos = function (callback) {
            scope.apenasLeitura = true;

            scope.getTiposDocumento(function () {
                scope.getTipoLogradouro(function () {
                    scope.getTiposNotificacao(function () {
                        scope.documentosIdentificacao(function () {
                            scope.getUsuario(function () {
                                scope.getTiposImovel(function () {
                                    scope.getZoneamentos(function () {
                                        scope.getEstados(function () {
                                            scope.getMunicipios(getJurisdicao(), function () {
                                                if (callback) {
                                                    callback();
                                                }
                                            });
                                        });
                                    });
                                });
                            });
                        });
                    });
                });
            });
        };

        scope.carregarRegistroDocumento = function (callback) {
            if(scope.relatorio.autuados && scope.relatorio.autuados.length > 0){
                for(var i = 0; i < scope.relatorio.autuados.length; i++){
                    if(scope.relatorio.autuados[i].ocorrenciaInfracao.areaAutuada){
                        if(scope.documento.sancaoAdministrativa && !scope.documento.sancaoAdministrativa.areaMulta){
                            scope.documento.sancaoAdministrativa.areaMulta = angular.copy(scope.relatorio.autuados[i].ocorrenciaInfracao.areaAutuada);
                        }
                    }
                }
            }

            if (!scope.documento.endereco) {
                scope.documento.endereco = {};
            }

            if (scope.documento.uploadArquivo == null) {
                scope.documento.uploadArquivo = false;
            }

            if (scope.documento.confirmaEndereco == null) {
                scope.documento.confirmaEndereco = true;
            }

            scope.documento.endereco.estado = getJurisdicao();

            if (scope.documento) {
                var k = 0;
                if (scope.documento.ufEmissao) {
                    for (k = 0; k < scope._estados.length; k++) {
                        if (scope.documento.ufEmissao === scope._estados[k].sigla) {
                            scope.documento.ufEmissao = scope._estados[k];
                            break;
                        }
                    }
                }
                if (scope.documento.endereco) {
                    if (scope.documento.endereco.idMunicipio) {
                        for (k = 0; k < scope._municipiosDocumento.length; k++) {
                            if (scope.documento.endereco.idMunicipio === scope._municipiosDocumento[k].id) {
                                scope.documento.endereco.municipio = scope._municipiosDocumento[k];
                                break;
                            }
                        }
                    }
                    if (scope.documento.endereco.cep) {
                        scope.desabilitaLogradouro = true;
                        scope.desabilitaMunicipio = true;
                        scope.desabilitaBairro = true;
                        scope.desabilitaTipoLogradouro = true;
                    }
                    if (scope.documento.endereco.tipoImovel) {
                        for (k = 0; k < scope._tiposImovel.length; k++) {
                            if (scope._tiposImovel[k].id === scope.documento.endereco.tipoImovel.id) {
                                scope.documento.endereco.tipoImovel = scope._tiposImovel[k];
                                break;
                            }
                        }
                    }

                    if (scope.documento.endereco.zoneamento) {
                        for (k = 0; k < scope._zoneamentos.length; k++) {
                            if (scope._zoneamentos[k].id === scope.documento.endereco.zoneamento.id) {
                                scope.documento.endereco.zoneamento = scope._zoneamentos[k];
                                break;
                            }
                        }
                    }
                }
            }

            if(scope.documento.tipoDocumento ===  "AUTO_DE_INFRACAO"){
                scope.getValorMulta();
            }

            if (scope.documento.dataNascimento) {
                scope.documento.dataNascimento = new Date(scope.documento.dataNascimento);
            }else{
                scope.documento.dataNascimento = "";
            }

            if (scope.documento.dataAutuacao) {
                scope.documento.dataAutuacao = new Date(scope.documento.dataAutuacao);
            }else{
                scope.documento.dataAutuacao = "";
            }

            scope.documento.responsavel = angular.copy(scope.autuado);

            if(callback){
                callback();
            }
        };

        scope.persistirRegistro = function(documento){
            if(scope.isEdicao){
                scope.editarDocumento(documento);
            }else{
                scope.salvar();
            }
        };

        scope.salvar = function () {
            if (!scope.documento.tipoDocumento) {
                ComumFactory.mostrarMensagemError('Informe o Tipo de Documento!');
                return false;
            }

            if(scope.documento.uploadArquivo === true){
                if (scope.documento.uploadArquivo ) {
                    scope.documento.endereco = null;
                }

                if (scope.documento.ufEmissao) {
                    scope.documento.ufEmissao = scope.documento.ufEmissao.sigla;
                }

                if(scope.documento.confirmaEndereco === true){
                    scope.documento.endereco = angular.copy(relatorioFiscalizacao.demanda.endereco);
                }

                if (scope.documento.endereco && scope.documento.endereco.municipio) {
                    scope.documento.endereco.idMunicipio = scope.documento.endereco.municipio.id;
                    scope.documento.endereco.nomeMunicipio = scope.documento.endereco.municipio.nome;
                }

                if (scope.documento.endereco && scope.documento.endereco.municipio) {
                    scope.documento.endereco.idMunicipio = scope.documento.endereco.municipio.id;
                    scope.documento.endereco.nomeMunicipio = scope.documento.endereco.municipio.nome;
                }

                $mdDialog.cancel();
            }else if (!$scope.formDocumento.$error.required && !$scope.formDocumento.$error.valid && !$scope.formDocumento.$error.cpf) {
                if(!$scope.formDocumento.$error.mindate && !$scope.formDocumento.$error.maxdate){
                    if (scope.documento.uploadArquivo) {
                        scope.documento.endereco = null;
                    }

                    if (scope.documento.ufEmissao) {
                        scope.documento.ufEmissao = scope.documento.ufEmissao.sigla;
                    }

                    if(scope.documento.confirmaEndereco === true){
                        scope.documento.endereco = angular.copy(relatorioFiscalizacao.demanda.endereco);
                        scope.documento.endereco.id = null;
                    }

                    if (scope.documento.endereco && scope.documento.endereco.municipio) {
                        scope.documento.endereco.idMunicipio = scope.documento.endereco.municipio.id;
                        scope.documento.endereco.nomeMunicipio = scope.documento.endereco.municipio.nome;
                    }

                    $mdDialog.cancel();
                }else{
                    ComumFactory.mostrarMensagemError('Data selecionada inválida!');
                }

            } else {
                ComumFactory.mostrarMensagemError('Preencha os campos corretamente!');
            }
        };

        scope.editarDocumento = function(documento){
            if($scope.formDocumento.$valid){
                if(scope.documento.confirmaEndereco === true){
                    scope.documento.endereco = angular.copy(relatorioFiscalizacao.demanda.endereco);
                    scope.documento.endereco.id = null;
                }

                if (scope.documento.endereco && scope.documento.endereco.municipio) {
                    scope.documento.endereco.idMunicipio = scope.documento.endereco.municipio.id;
                    scope.documento.endereco.nomeMunicipio = scope.documento.endereco.municipio.nome;
                }

                service.editarDocumento(documento).then(function(){
                    $mdDialog.cancel();
                });
            }else{
                ComumFactory.mostrarMensagemError('Preencha os campos corretamente!');
            }
        };

        scope.validaAnoInicial = function (ano) {
            if (new Date().getFullYear() - ano > 6 || new Date().getFullYear() < ano) {
                scope.documento.sancaoAdministrativa.anoInicialOcupacao = null;
            }
        };

        scope.validaAnoFinal = function (ano) {
            if (ano > new Date().getFullYear()) {
                scope.documento.sancaoAdministrativa.anoFinalOcupacao = null;
            }
        };

        scope.validaIntervaloCobranca = function () {
            var ini = scope.documento.sancaoAdministrativa.anoInicialCobranca;
            var fim = scope.documento.sancaoAdministrativa.anoFinalCobranca;

            if (ini && fim) {
                if (fim - ini > 5) {
                    scope.documento.sancaoAdministrativa.anoInicialCobranca = null;
                    scope.documento.sancaoAdministrativa.anoFinalCobranca = null;
                } else {
                    scope.criaMultaPorAno(fim - ini);
                }
            }
        };

        scope.criaMultaPorAno = function (periodo) {
            scope.documento.sancaoAdministrativa.multas = [];
            for (var i = 0; i <= periodo; i++) {
                scope.documento.sancaoAdministrativa.multas[i] = {};
                scope.documento.sancaoAdministrativa.multas[i].ano = Number(scope.documento.sancaoAdministrativa.anoInicialCobranca) + i;
            }

        };

        scope.calculaVdp = function (multa) {
            multa.valorDominioPleno = multa.plantaValoresGenericos * scope.documento.sancaoAdministrativa.areaOcupacao;
            scope.calculaIndenizacaoVDP(multa);
        };

        scope.calculaIndenizacaoVDP = function (multa) {
            multa.indenizacao = multa.valorDominioPleno/10;
            scope.calculaIndenizacao()
        };

        scope.calculaIndenizacao = function () {
            var total = 0;
            for (var i = 0; i < scope.documento.sancaoAdministrativa.multas.length; i++) {
                if (scope.documento.sancaoAdministrativa.multas[i].indenizacao) {
                    total += scope.documento.sancaoAdministrativa.multas[i].indenizacao;
                }
            }
            scope.documento.sancaoAdministrativa.valorTotalIndenizacao = total;

        };

        scope.calcularMulta = function () {
            scope.documento.sancaoAdministrativa.totalMulta = Number(scope.documento.sancaoAdministrativa.valorMulta.valorMulta) * Number(scope.documento.sancaoAdministrativa.areaMulta);
        };

        scope.getCEPDocumento = function (cep) {
            if (cep && cep.length === 8) {
                scope._endereco = {};
                integradorService.getCEP(cep).then(function (objectReturn) {
                    if (!objectReturn.data) {
                        ComumFactory.mostrarMensagemError('CEP não encontrado!');
                        scope.documento.endereco = {};
                        scope.documento.endereco.estado = getJurisdicao();
                        habilitaCampoCEP();
                    }
                    if (objectReturn.data.uf !== scope.documento.endereco.estado) {
                        ComumFactory.mostrarMensagemError('O CEP informado não pertence a sua Unidade Federativa!');
                        if (scope.documento.endereco) {
                            scope.documento.endereco = {};
                            scope.documento.endereco.estado = getJurisdicao();
                            habilitaCampoCEP();
                        }
                        return;
                    }

                    var retorno = objectReturn.data;
                    scope.documento.endereco.logradouro = retorno.logradouro;
                    scope.desabilitaLogradouro = true;

                    for (var obj in scope._tipoLogradouro) {
                        if (scope._tipoLogradouro[obj] === retorno.tipoLogradouro) {
                            scope._registro.endereco.tipoLogradouro = obj;
                            scope.desabilitaTipoLogradouro = true;
                        }
                    }

                    scope.documento.endereco.bairro = retorno.bairro;
                    scope.desabilitaBairro = true;

                    for (var i = 0; i < scope._municipiosDocumento.length; i++) {
                        if (scope._municipiosDocumento[i].codigo == retorno.codMunicipio) {
                            scope.documento.endereco.municipio = scope._municipiosDocumento[i];
                            scope.desabilitaMunicipio = true;
                            break;
                        }
                    }


                }, function () {
                    ComumFactory.mostrarMensagemError('CEP não encontrado!');
                    scope.documento.endereco = {};
                    scope.documento.endereco.estado = getJurisdicao();
                    habilitaCampoCEP();
                });
            } else {
                ComumFactory.mostrarMensagemError('Informe um CEP válido!');
                scope.documento.endereco = {};
                scope.documento.endereco.estado = getJurisdicao();
                habilitaCampoCEP();
            }
        };

        scope.getTiposDocumento = function (callback) {
            scope._tiposDocumento = {};
            service.getTiposDocumento().then(function (objectReturn) {
                scope._tiposDocumento = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        scope.getTiposNotificacao = function (callback) {
            service.getTiposNotificacao().then(function (objectReturn) {
                scope._tiposNotificacao = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        scope.documentosIdentificacao = function (callback) {
            service.documentosIdentificacao().then(function (objectReturn) {
                scope._documentosIdentificacao = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        scope.mudaDocumentoIdentificacao = function (documentoIdentificacao) {
            if (documentoIdentificacao == 'PASSAPORTE') {
                scope.documento.orgaoEmissor = null;
                scope.documento.ufExpedicao = null;
            }
            if (documentoIdentificacao == 'RG') {
                scope.documento.paisEmissor = null;
            }
        };

        scope.getMunicipios = function (sigla, callback) {
            if (sigla) {
                scope._municipiosDocumento = {};
                integradorService.getMunicipios(sigla).then(function (objectReturn) {
                    scope._municipiosDocumento = objectReturn.data;

                    if (callback) {
                        callback();
                    }
                });
            }
        };

        scope.getTipoLogradouro = function (callback) {
            scope._tipoLogradouro = {};
            service.getTipoLogradouro().then(function (objectReturn) {
                scope._tipoLogradouro = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        scope.getUsuario = function (callback) {
            scope._usuario = {};
            service.getUsuario().then(function (objectReturn) {
                scope._usuario = objectReturn.data;
                getJurisdicao();

                if (callback) {
                    callback();
                }
            });
        };

        scope.getTiposImovel = function (callback) {
            scope._tiposImovel = {};
            service.getTiposImovel().then(function (objectReturn) {
                scope._tiposImovel = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.getZoneamentos = function (callback) {
            scope._zoneamentos = {};
            service.getZoneamentos().then(function (objectReturn) {
                scope._zoneamentos = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        scope.getEstados = function (callback) {

            if (!scope._estados) scope._estados = [];
            if (scope._estados.length > 0) return;

            integradorService.getEstados().then(function (objectReturn) {
                scope._estados = angular.copy(objectReturn.data);

                if (callback) {
                    callback();
                }
            });
        };

        function getJurisdicao() {

            if (!scope._usuario.jurisdicoes || scope._usuario.jurisdicoes.length > 1) {
                return 'DF';
            } else {
                return scope._usuario.jurisdicoes[0];
            }

        }

        scope.cancelModal = function () {
            scope.documento = null;
            $mdDialog.cancel();
        };

        scope.adicionarArquivoDocumento = function (file) {
            scope.apenasLeitura = true;
            scope.realizandoUpload = true;
            service.upload(file[0]).then(function (objectReturn) {
                scope.documento.arquivo = objectReturn.data;

                scope.apenasLeitura = false;
                scope.realizandoUpload = false;
            });
        };

        scope.changeUpload = function (upload) {
            if (!upload) {
                limpaCampos();
            }
        };

        scope.changeCep = function (cep) {
            if (!cep) {
                scope.cepPesquisado = false;
            }
        };

        scope.getValorMulta = function (callback) {
            service.getValorMulta().then(function (objectReturn) {
                scope.documento.sancaoAdministrativa.valorMulta = objectReturn.data;

                scope.calcularMulta();
                if(callback){
                    callback();
                }
            });
        };


        scope.calcularIndenizacaoArea= function(multas){
            if(multas && multas.length > 0){
                for(var i = 0; i < multas.length; i++){
                    scope.calculaVdp(multas[i]);
                }
            }
        };

        function limpaCampos() {
            scope.documento.dataAutuacao = null;
            scope.documento.horaAutuacao = null;
            scope.documento.areaEmbargo = null;
            scope.documento.responsavel = null;
            scope.documento.documentoIdentificacao = null;
            scope.documento.numeroDocumento = null;
            scope.documento.paisEmissor = null;
            scope.documento.orgaoEmissor = null;
            scope.documento.ufEmissao = null;
            scope.documento.filiacao = null;
            scope.documento.dataNascimento = null;
            scope.documento.nacionalidade = null;
            scope.documento.naturalidade = null;
            scope.documento.confirmaEndereco = true;
            scope.documento.descricaoFato = null;
            scope.documento.emailContato = null;
            scope.documento.telefoneContato = null;
            scope.documento.endereco = null;
        }

        function habilitaCampoCEP() {
            scope.desabilitaLogradouro = false;
            scope.desabilitaMunicipio = false;
            scope.desabilitaBairro = false;
            scope.desabilitaTipoLogradouro = false;
        }

        scope.initDoc();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('RelatorioDetalharController', ['$scope','$rootScope', '$filter', '$mdDialog', 'RelatorioService', 'ComumFactory', '$state', 'IntegradorService', 'BuscarCpfServiceFiscalizacao', 'ciMapaLayersFactoryFiscalizacao', 'ciMapaViewFactoryFiscalizacao', '$timeout', '$stateParams', '$q', 'ArquivoServiceFiscalizacao', 'NotificadorServiceFiscalizacao', 'MAPA_CONSTANTS_FISCALIZACAO', 'CartografiaServiceFiscalizacao', controller]);

    function controller($scope, $rootScope, $filter, $mdDialog, service, ComumFactory, $state, integradorService, BuscarCpfServiceFiscalizacao, ciMapaLayersFactoryFiscalizacao, ciMapaViewFactoryFiscalizacao, $timeout, $stateParams, $q, ArquivoServiceFiscalizacao, NotificadorServiceFiscalizacao, MAPA_CONSTANTS_FISCALIZACAO, CartografiaServiceFiscalizacao) {
        var scope = this;
        var vm = $scope;

        scope.init = function () {
            scope.novoRegistro(function () {});
        };

        scope.pesquisarUf = [];
        scope.limparPesquisa = function(){
            scope.pesquisarUf = '';
        }


        window.mdSelectOnKeyDownOverride = function(event) { event.stopPropagation(); };

        vm.validarCpfAutuado = function (cpf) {
            if (cpf != null) {
                cpf = cpf.replace(/[^0-9]/g, '');
                if (cpf.length === 11) {
                   vm.buscarPessoaCpf(cpf, 'A')
                } else if (cpf.length === 14) {
                    vm.buscarRegistorCnpj(cpf, 'A')
                }else{
                    ComumFactory.mostrarMensagemError("CPF ou CNPJ inválido");
                }
            }
        };

        vm.validarCpfResponsavelDemanda = function (cpf) {
            if (cpf != null) {
                cpf = cpf.replace(/[^0-9]/g, '');
                if (cpf.length === 11) {
                   vm.buscarPessoaCpf(cpf, 'D')
                } else if (cpf.length === 14) {
                    vm.buscarRegistorCnpj(cpf, 'D')
                }else{
                    ComumFactory.mostrarMensagemError("CPF ou CNPJ inválido");
                }
            }
        };

        vm.validarCpfOcupante = function (cpf) {
            if (cpf != null) {
                cpf = cpf.replace(/[^0-9]/g, '');
                if (cpf.length === 11) {
                    vm.buscarPessoaCpf(cpf, 'O')
                } else if (cpf.length === 14) {
                    vm.buscarRegistorCnpj(cpf, 'O')
                }else{
                    ComumFactory.mostrarMensagemError("CPF ou CNPJ inválido");
                }
            }
        };

        vm.buscarRegistorCnpj = function (cpf, campo) {
            BuscarCpfServiceFiscalizacao.buscarCnpj(cpf).then(function (response) {
                if (campo == 'A') {
                    scope._autuado.responsavel.nome = response.data.nome;
                    scope.autuadoNomeAutomatico = true;
                } else if (campo == 'D') {
                    scope._registro.demanda.responsavel.nome = response.data.nome;
                    scope.demandaNomeAutomatico = true;
                } else {
                    scope._registro.responsavel.nome = response.data.nome;
                    scope.ocupanteNomeAutomatico = true;
                }

            }, function () {
                cpf = '';
                if (campo == 'A') {
                    scope.autuadoNomeAutomatico = false;
                } else if (campo == 'D') {
                    scope.demandaNomeAutomatico = false;
                } else {
                    scope.ocupanteNomeAutomatico = false;
                }
                ComumFactory.mostrarMensagemError("CNPJ não encontrado");
            })
        };

        vm.buscarPessoaCpf = function (cpf, campo) {
            BuscarCpfServiceFiscalizacao.buscarPessoa(cpf).then(function (response) {
                if (campo == 'A') {
                    scope._autuado.responsavel.nome = response.data.nome;
                    scope.autuadoNomeAutomatico = true;
                } else if (campo == 'D') {
                    scope._registro.demanda.responsavel.nome = response.data.nome;
                    scope.demandaNomeAutomatico = true;
                } else {
                    scope._registro.responsavel.nome = response.data.nome;
                    scope.ocupanteNomeAutomatico = true;
                }
            }, function () {
                cpf = '';
                if (campo == 'A') {
                    scope.autuadoNomeAutomatico = false;
                } else if (campo == 'D') {
                    scope.demandaNomeAutomatico = false;
                } else {
                    scope.ocupanteNomeAutomatico = false;
                }
                ComumFactory.mostrarMensagemError("CPF não encontrado");
            })
        };

        /**
         * Verifica a ação que o usuário está realizando e inicia as funções comforme as permições
         * @param callback
         */
        scope.validarState = function(callback){
            if ($stateParams.id) {
                if ($state.current.name.indexOf('consultar') !== -1) {
                    scope.mostraFiltrosIncluir = false;

                    scope.findBy($stateParams.id, function () {
                        scope.getDocumentos(scope._registro);
                        scope.verificaDocumentoPendente(scope._registro);

                        scope.btnCroqui = false;
                        scope.apenasLeitura = true;
                        scope.autuadoBlock = true;
                        scope.showConluirTab = true;
                        scope.documentoPendente = true;
                    });
                } else if ($state.current.name.indexOf('validar') !== -1) {
                    scope.mostraFiltrosIncluir = false;

                    scope.findBy($stateParams.id, function () {
                        scope.getDocumentos(scope._registro);
                        scope.verificaDocumentoPendente(scope._registro);

                        scope.btnCroqui = false;
                        scope.apenasLeitura = true;
                        scope.mostraBtnValidar = true;
                        scope.autuadoBlock = true;
                        scope.showConluirTab = true;
                    });
                } else if ($state.current.name.indexOf('alterar') !== -1) {
                    scope.mostraFiltrosIncluir = false;
                    scope.findBy($stateParams.id, function () {
                        service.getDocumentoStatus().then(function(objectResponse){
                            scope._documentoStatus  = objectResponse.data;

                            scope.getDocumentos(scope._registro);
                            scope.verificaDocumentoPendente(scope._registro);
                            scope.apenasLeitura = false;
                            scope.showConluirTab = true;

                            if(scope._registro.autuados)
                                scope._registro.autuados = scope._registro.autuados.map(function(au){
                                    au.ocorrenciaInfracao.concluido = (au.ocorrenciaInfracao.perguntaUm !== null) ? true : false;
                                    return au;
                                });

                            if($state.params.focarAbaAutuado) {
                                scope._registro.ocorrenciaDeInfracao = true;
                                $scope.tabIndex = 1;
                            }
                        });
                    });
                }
            }else{
                scope.apenasLeitura = false;
                scope.mostraFiltrosIncluir = true;
            }

            if(callback){
                callback();
            }
        };

        /**
         * Inicia um novo registro
         * @param callback
         */
        scope.novoRegistro = function (callback) {
            //Flag que verifica se a página está em modo leitura
            scope.apenasLeitura = true;

            //objetos
            scope._registro = {};
            scope._registro.ordemFiscalizacao = {};
            scope._registro.responsavel = {};
            scope._registro.endereco = {};
            scope._registro.demanda = {};
            scope._registro.arquivoCroqui = null;
            scope.documento = {};
            scope.documento.endereco = {};
            scope._autuado = {};
            scope._autuado.autuadoNaoIdentificado = false;

            //listas
            scope._registro.autuados = [];
            scope._registro.coordenadasBenfeitorias = [];
            scope._estados = [];
            scope._relatorios = [];
            scope._ordemFiscalizacao = [];
            scope._status = [];
            scope._fiscais = [];
            scope._tiposDocumento = [];
            scope._documentosIdentificacao = [];
            scope._tiposNotificacao = [];
            scope._documentos = [];
            scope._miniaturasRelatorioFotografico = [];

            //booleanos
            scope._registro.isExecutada = false;
            scope._registro.confirmaEndereco = false;
            scope._registro.confirmaResponsavel = false;
            scope._registro.importarResponsavel = false;
            scope._registro.ocorrenciaDeInfracao = false;
            scope.confirmaCroqui = false;
            scope.documento.confirmaEndereco = false;
            scope.documento.upload = false;
            scope.executado = false;
            scope.mostraFiltrosIncluir = false;
            scope.apenasLeitura = false;
            scope.mostraBtnValidar = false;
            scope.btnExcluirDoc = true;
            scope.isDesabilitado = false;
            scope.mostrarOcorrenciaInfracao = false;
            scope.showAreaAutuada = false;
            scope.btDesenhar = false;
            scope.ocorrenciaInfracaoValid = true;
            scope.documentoPendente = false;
            scope.showConluirTab= false;
            scope.autuadoNomeInvalido = false;
            scope.autuadoCpfCnpjInvalido = false;
            scope.autuadoProcessoSeiInvalido = false;

            scope._registro.dtFiscalizacao = '';

            ciMapaLayersFactoryFiscalizacao.init();
            ciMapaViewFactoryFiscalizacao.init();

            scope.getTipoLogradouro(function(){
                scope.getUsuario(function(){
                    scope.getTiposImovel(function(){
                        scope.getZoneamentos(function(){
                            scope.getTiposDocumento(function(){
                                scope.getStatus(function(){
                                    scope.getTiposDocumento(function(){
                                        scope.validarState(function(){
                                                if (($state.current.name.indexOf('incluir') !== -1)){
                                                    scope.getOrdensFiscalizacao(function(){});
                                                }
                                        });
                                    });
                                });
                            });
                        });
                    });
                });
            });

            if (callback) {
                callback();
            }
        };

        /**
         * realiza uma pesquisa de relatório de fiscalização pelo seu ID retornando suas informações
         * @param registroId
         * @param callback
         */
        scope.findBy = function (registroId, callback) {
            service.findBy(registroId).then(function (objectResponse) {
                scope._registro = angular.copy(objectResponse.data);

                !scope._registro.autuados ? scope._registro.autuados = [] : "";

                scope._registro.dtFiscalizacao ? scope._registro.dtFiscalizacao = new Date(scope._registro.dtFiscalizacao) : scope._registro.dtFiscalizacao = '';

                if (scope._registro.ordemFiscalizacao.inicio) {
                    scope.minDataFiscalizacao = new Date(scope._registro.ordemFiscalizacao.inicio);
                }

                if (scope._registro.croqui) {
                    scope._registro.croqui = JSON.parse(scope._registro.croqui);
                }

                if (!scope._registro.dtFiscalizacao) {
                    scope._registro.dtFiscalizacao = '';
                }

                if(scope._registro.endereco && scope._registro.endereco.idMunicipio){
                    var idMunicipio = scope._registro.endereco.idMunicipio;
                    var nomeMunicipio = scope._registro.endereco.nomeMunicipio;
                    scope._registro.endereco.municipio = {id: idMunicipio, nome: nomeMunicipio };
                }

                var content_type = ['image/img', 'image/jpeg', 'image/png', 'image/shp'];

                if (scope._registro.arquivoCroqui && scope._registro.arquivoCroqui.id) {
                    if(scope._registro.arquivoCroqui.contentType === "application/zip"|| scope._registro.arquivoCroqui.contentType === "application/x-rar-compressed") {
                        scope.tipoArquivo = "shapeFile";
                        scope.btDesenhar = false;
                    }else if(content_type.indexOf(scope._registro.arquivoCroqui.contentType) !== -1){
                        scope.tipoArquivo = "croqui";
                        scope.btDesenhar = false;
                    }
                }else if(!scope._registro.arquivoCroqui){
                    scope.tipoArquivo = "desenho";
                    scope.btDesenhar = true;
                }

                scope._fiscais = angular.copy(scope._registro.ordemFiscalizacao.fiscais);

                if (scope._registro.tipoImovel && scope._registro.tipoImovel.id) {
                    for (var j = 0; j < scope._tiposImovel.length; j++) {
                        if (scope._tiposImovel[j].id === scope._registro.tipoImovel.id) {
                            scope._registro.tipoImovel = scope._tiposImovel[j];
                            scope.mostrarOcorrenciaInfracao = true;
                            !scope._registro.confirmaResponsavel && scope._registro.tipoImovel && scope._registro.tipoImovel.tipo ? scope.obrigatoriedadeOcupante = true : scope.obrigatoriedadeOcupante = false;
                            break;
                        }
                    }
                }

                if(scope._registro.autuados && scope._registro.autuados.length > 0){
                    for(var k = 0; k < scope._registro.autuados.length; k++){
                        scope.setStatusAutuado(scope._registro.autuados[k]);
                    }
                }

                scope.gerarRelatorio = true;
                scope.isRelatorio = true;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém as demandas pertencentes a uma ordem de fiscalização
         * @param ordemFiscalizacao
         */
        scope.showDemandas = function (ordemFiscalizacao) {
            scope.maxDataFiscalizacao = angular.copy(new Date(ordemFiscalizacao.fim));
            scope.minDataFiscalizacao = angular.copy(new Date(ordemFiscalizacao.inicio));

            $rootScope.maxDataFiscalizacao = angular.copy(scope.maxDataFiscalizacao);
            $rootScope.minDataFiscalizacao = angular.copy(scope.minDataFiscalizacao);

            for( var i = 0; i < scope._ordemFiscalizacao.length; i++){
                if(ordemFiscalizacao.id === scope._ordemFiscalizacao[i].id){
                    scope._ordemFiscalizacao[i].showDemandas = true;
                }else{
                    scope._ordemFiscalizacao[i].showDemandas = false;
                }
            }

            scope.getRelatorios(ordemFiscalizacao);
        };

        /**
         * Obtém uma lista de relatórios de fiscalização
         * @param ordemFiscalizacao
         * @param demandas
         */

        scope.getRelatorios = function(ordemFiscalizacao){
            service.getRelatorioOrdemFiscalizacaoEmJurisdicao(ordemFiscalizacao.id).then(function (objectReturn) {
                scope._relatorios = objectReturn.data;
                scope.relatoriosCopy = angular.copy(scope._relatorios);

                for (var i = 0; i < scope._relatorios.length; i++) {

                            scope._relatorios[i].status === 'NAO_POSSUI' ? scope._relatorios[i].demanda.showNovoRelatorio = true : scope._relatorios[i].demanda.showNovoRelatorio = false;


                }
            });
        };

        /**
         * Verifica a demanda selecionada e obtém o seu relatório
         * @param ordemFiscalizacao
         * @param demanda
         */
        scope.handleDemandaSelected = function (ordemFiscalizacao, demanda, callback) {
            scope.resetaFormulario();

            scope.demandaSelected = true;
            if (!scope.relatoriosCopy) {
                scope.relatoriosCopy = angular.copy(scope._relatorios);
            }

            scope._registro.ordemFiscalizacao ? scope._registro.ordemFiscalizacao = angular.copy(ordemFiscalizacao) : "";
            scope._registro.demanda ? scope._registro.demanda = angular.copy(demanda) : "";

            if (scope._relatorios && scope._relatorios.length > 0) {
                for (var i = 0; i < scope._relatorios.length; i++) {
                    if (demanda.id === scope.relatoriosCopy[i].demanda.id) {
                        scope._relatorios = [];
                        scope._relatorios.push(angular.copy(scope.relatoriosCopy[i]));
                        scope._registro.id = angular.copy(scope.relatoriosCopy[i].id);
                        scope._fiscais = scope.relatoriosCopy[0].ordemFiscalizacao.fiscais;
                        return;
                    }
                }
            }

            if(callback){
                callback();
            }

        };

        scope.resetaFormulario = function(){
            scope._registro.ordemFiscalizacao = {};
            scope._registro.endereco = {};
            scope._registro.demanda = {};
            scope._registro.responsavel = {};
            scope._registro.isExecutada = false;
            scope._registro.autuados = [];
            scope.gerarRelatorio = false;

            $scope.formRegistro.$setPristine();
            $scope.formRegistro.$setUntouched();
        };

        /**
         * Conclui o relatório de fiscaliação
         * @param registro
         */
        scope.concluir = function (registro) {
            if (registro.justificativa) {
                service.concluir(registro).then(function (objectReturn) {
                    scope.cancelModal();
                    $state.go('fiscalizacao.relatorio.listar');
                });
            } else {
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
            }
        };

        /**
         * Gera o relatório de fiscalização
         */
        scope.gerar = function () {
            scope.gerarRelatorio = true;
            scope.isRelatorio = true;

            if (scope._relatorios[0].status !== 'NAO_POSSUI' && scope._relatorios[0].status !== 'EM_CONSTRUCAO') {
                scope.executado = true;
            }

            scope._registro.confirmaEndereco = false;
            scope._registro.confirmaResponsavel = false;

            scope._registro.areaImovel = angular.copy(scope._registro.demanda.areaImovel);
            scope._registro.endereco = angular.copy(scope._registro.demanda.endereco);

            scope.getMunicipios((scope._registro.endereco.estado), function() {
                for (var i = 0; i < scope._municipios.length; i++) {
                    if (scope._municipios[i].id === scope._registro.endereco.idMunicipio) {
                        scope._registro.endereco.municipio = scope._municipios[i];
                        break;
                    }
                }
            });

            if(scope._registro.endereco.zoneamento != null){
                for (var i = 0; i < scope._zoneamentos.length; i++) {
                    if (scope._zoneamentos[i].id === scope._registro.endereco.zoneamento.id) {
                        scope._registro.endereco.zoneamento = scope._zoneamentos[i];
                        break;
                    }
                }
            }
        };

        // Quando salvar as abas que não forem o questionário, não permitir salvar alterações no autuado
        scope.verificarAutuadosInfoOrdemFiscalizacao = function(autuados){
            return !autuados ? null : autuados
            .filter(function(au){ return au.id; }) // não salva os novos autuados
            .map(function(au){
                if(au.ocorrenciaInfracao.concluido)
                    return au;
                else{
                    au.ocorrenciaInfracao = { id: au.ocorrenciaInfracao.id }
                    return au;
                }
            })
        }

        scope.verificarAutuadosCadastrarAutuado = function(autuados){
            return !autuados ? null : autuados.map(function(au){
                if(au.responsavel.cpfCnpj != '-') {
                    if (au.ocorrenciaInfracao.concluido)
                        return au;
                    else {
                        au.ocorrenciaInfracao = {id: au.ocorrenciaInfracao.id}
                        return au;
                    }
                }else{
                    return au;
                }
            });
        }

        scope.verificarAutuadosQuestionarioAutuado = function(autuados, cpfCnpj){
            return !autuados ? null : autuados
            .filter(function(au){ return (au.id || au.responsavel.cpfCnpj === cpfCnpj); }) // não salva os novos autuados
            .map(function(au){
                console.log("testando:",au);
                if(au.ocorrenciaInfracao.concluido || au.responsavel.cpfCnpj === cpfCnpj)
                    return au;
                else{
                    au.ocorrenciaInfracao = { id: au.ocorrenciaInfracao.id }
                    return au;
                }
            })
        }



        /**
         * Salva o relatório de fiscalização
         * @param registro
         */
        scope.persistirRegistro = function (registro) {
            if(scope.imgMapa){
                scope.uploadImgMapa(registro, function () {
                    scope.salvar(registro);
                });
            }else{
                scope.salvar(registro);
            }
        };

        scope.salvarFormRegistro = function (registro) {
            $scope.formInfoOrdemFiscalizacao.$setSubmitted();
            var validacao = scope.validarFormsAutuados();
            if(validacao)
                scope.persistirRegistro(registro);
        }

        scope.salvarTabInfoOrdemFiscalizacao = function (registro) {
            $scope.formInfoOrdemFiscalizacao.$setSubmitted();
            if($scope.formInfoOrdemFiscalizacao.$valid){
                registro.autuados = scope.verificarAutuadosInfoOrdemFiscalizacao(registro.autuados);
                scope.persistirRegistro(registro);

            }else {
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
            }
        };

        scope.salvarTabCadastroAutuado = function (registro) {
            registro.autuados = scope.verificarAutuadosCadastrarAutuado(registro.autuados);
            scope.persistirRegistro(registro);
        };

        scope.salvarTabQuestionarioAutuado = function (registro, cpfCnpj) {
            var nomeForm = "formQuestionarioAutuado" + cpfCnpj;
            var scopeForm = angular.element("[name=" + nomeForm + "]").scope();
            var formQuestionarioAutuado = scopeForm[nomeForm];

            formQuestionarioAutuado.$setSubmitted();
            console.log("teste",formQuestionarioAutuado.$valid);
            if(formQuestionarioAutuado.$valid){
                registro.autuados = scope.verificarAutuadosQuestionarioAutuado(registro.autuados, cpfCnpj)
                scope.persistirRegistro(registro);
            }else {
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
            }
        };

        scope.validarFormsAutuados = function () {
            var autuados = scope._registro.autuados ? scope._registro.autuados : [];
            var retorno = true;
            autuados.forEach(function(au) {
                if(au.responsavel.cpfCnpj != '-') {
                    var nomeForm = "formQuestionarioAutuado" + au.responsavel.cpfCnpj;
                    var scopeForm = angular.element("[name=" + nomeForm + "]").scope();
                    var formQuestionarioAutuado = scopeForm[nomeForm];
                    formQuestionarioAutuado.$setSubmitted();
                    if (formQuestionarioAutuado.$invalid)
                        retorno = false;
                }
            });
            return retorno;
        }


        /**
         * Função responsável por persistir o relatório de fiscalização.
         * @param registro
         */
        scope.salvar = function(registro) {
            if ($scope.formRegistro.$valid) {
                registro.croqui = JSON.stringify(registro.croqui);

                if (registro.confirmaResponsavel) {
                    registro.responsavel = null;
                }

                if (registro.confirmaEndereco) {
                    registro.endereco = registro.demanda.endereco;
                } else {
                    registro.endereco.idMunicipio = registro.endereco.municipio.id;
                    registro.endereco.nomeMunicipio = registro.endereco.municipio.nome;
                    registro.endereco.municipio = null;
                }

                service.salvar(registro).then(function (objectReturn) {
                    $state.go('fiscalizacao.relatorio.listar');
                });
            } else {
                if ($scope.formRegistro.$error.required) {
                    ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
                } else if ($scope.formRegistro.$error.cnpj || $scope.formRegistro.$error.cpf) {
                    ComumFactory.mostrarMensagemError('CPF/CNPJ Inválido!');
                } else if ($scope.formRegistro.$error.mindate || $scope.formRegistro.$error.maxdate) {
                    ComumFactory.mostrarMensagemError('Data da fiscalização selecionada inválida!');
                }
            }
        };

        scope.autuarDocumento = function(documentoId){
            if (scope._registro.revalidacao == null || scope._registro.revalidacao == undefined ? false : scope._registro.revalidacao) {
                ComumFactory.mostrarMensagemError('Essa ação não pode ser executada enquanto a Ordem de Fiscalização estiveraguardando revalidação');
            }else{
            service.autuarDocumento($stateParams.id, documentoId).then(function(){
                $state.go("fiscalizacao.relatorio.alterar",{"id": $stateParams.id},{reload:true});
            });
        }
        };

        /**
         * Realiza um upload de mapa
         * @param registro
         * @param callback
         */
        scope.uploadImgMapa = function(registro, callback) {
            var mapa = dataURItoBlob(scope.imgMapa);
            ArquivoServiceFiscalizacao.uploadArquivo(mapa).then(function (retorno) {
                registro.imagemMapa = retorno.data;
                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Abre a modal de justificativa da conclusão do relatório de fiscalização
         * @param ev
         */
        scope.showModalJustificativa = function (ev) {
            if (!scope._registro.isExecutada && scope._registro.ordemFiscalizacao.id && scope._registro.demanda.id) {
                if (scope._relatorios[0].status === 'NAO_POSSUI') {
                    $mdDialog.show({
                        controller: function () {
                            return scope;
                        },
                        controllerAs: 'scope',
                        templateUrl: 'scripts-fiscalizacao/pages/relatorio/detalhar/view/relatorio-modal.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: true,
                        fullscreen: true,
                        onComplete: function () {
                            scope.scrollToTop();
                        }
                    });
                } else {
                    ComumFactory.mostrarMensagemError('Demanda já concluída');
                }
            } else {
                ComumFactory.mostrarMensagemError('Preencha todos os Campos');
            }
        };

        /**
         * Fecha a modal de justificativa da conclusão do relatório de ordem de fiscalização
         */
        scope.cancelModal = function () {
            scope._registro.justificativa = '';
            $mdDialog.cancel();

            if (scope.apenasLeitura === true) {
                scope.apenasLeitura = false;
            }

            if (scope.documento) {
                scope.documento = {};
            }
        };

        /**
         * Abre a modal de justificativa de uma nova demanda
         * @param ev
         */
        scope.showModalJustificativaNovaDemanda = function (ev, ordemFiscalizacao) {

            $mdDialog.show({
                controller: 'NovaDemandaController as scope',
                templateUrl: 'scripts-fiscalizacao/pages/relatorio/nova-demanda/view/justificativa-nova-demanda-modal.tmpl.html',
                parent: angular.element(document.body),
                targetEvent: event,
                clickOutsideToClose: true,
                fullscreen: true,
                autoWrap: false,
                disableParentScroll: true,
                focusOnOpen: true,
                locals: {
                    ordemFiscalizacao: ordemFiscalizacao,
                    apenasLeitura: scope.apenasLeitura
                },
                onComplete: function () {
                    scope.ordemFiscalizacao = angular.copy(ordemFiscalizacao);
                    scope.apenasLeitura = angular.copy(scope.apenasLeitura);
                }
            });
        };

        /**
         * Ao abrir uma modal, realiza um scroll automatico para o topo da tela
         */
        scope.scrollToTop = function () {
            var elmnt = document.getElementById('modal');
            elmnt.scrollTop = 0;
        };

        /**
         * Verifica o confirmação do ocupante selecionado e realiza as devidas validações
         */
        scope.handleOcupanteSelected = function () {
            if (scope._registro.confirmaResponsavel) {
                if (scope._registro.responsavel) {
                    delete scope._registro.responsavel;
                }
                scope.obrigatoriedadeOcupante = false;
            } else {
                !scope._registro.responsevel ? scope._registro.responsevel = {} : '';
                !scope._registro.confirmaResponsavel && scope._registro.tipoImovel && scope._registro.tipoImovel.tipo ? scope.obrigatoriedadeOcupante = true : '';
            }
        };

        /**
         * Verifica a confirmação do endereço selecionado e realiza as devidas validações
         */
        scope.handleEnderecoSelected = function () {
            if (scope._registro.confirmaEndereco === true) {
                if (scope._registro.endereco) {
                    delete scope._registro.endereco;
                }
            } else if (!scope._registro.confirmaEndereco) {
                scope.isRelatorio = true;
            }
        };

        /**
         * Adiciona um arquivo (img, jpeg, png) ao relatório de fiscalização
         * @param file
         */
        scope.adicionarCroqui = function (file) {
            var content_type = ['image/img', 'image/jpeg', 'image/png'];

            if (content_type.indexOf(file[0].type) !== -1) {
                service.upload(file[0]).then(function (objectReturn) {
                    scope._registro.arquivoCroqui = objectReturn.data;

                    scope.apenasLeitura = false;
                });

            } else {
                ComumFactory.mostrarMensagemError('O formato da imagem é inválido');
            }
        };

        /**
         * Adiciona um arquivo zip do tipo(shapeFile) ao relatório de fiscalização
         * @param file
         */
        scope.adicionarShapeFile = function(file){
            service.uploadShapeFile(file[0]).then(function (objectReturn) {
                scope._registro.arquivoCroqui = objectReturn.data;

                if(scope._registro.arquivoCroqui.geometry){
                    scope._registro.croqui = scope._registro.arquivoCroqui.geometry;
                }

                NotificadorServiceFiscalizacao.notify(scope, scope._registro.arquivoCroqui.geometry, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.LIMPAR_DESENHO);
                NotificadorServiceFiscalizacao.notify(scope, scope._registro.arquivoCroqui.geometry, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.MARCAR_PONTOS_SHAPE_FILE);

                scope.apenasLeitura = false;
            });
        };

        /**
         * verifica o tipo de croqui selecionado para realizar as devidas validações
         * @param value
         */
        scope.handleCroquiSelected = function (value) {
            if (value === 'croqui' || value === "shapeFile") {
                scope._registro.arquivoCroqui = null;
                scope._registro.croqui = '';
                scope.btDesenhar = false;
            }else{
                scope._registro.arquivoCroqui = null;
                scope.btDesenhar = true;
            }
        };

        /**
         * Valida o relatório de fiscalização
         * @param registro
         */
        scope.validar = function (registro) {
            if (scope._registro.revalidacao == null || scope._registro.revalidacao == undefined ? false : scope._registro.revalidacao) {
                ComumFactory.mostrarMensagemError('Essa ação não pode ser executada enquanto a Ordem de Fiscalização estiveraguardando revalidação');
            }else{
            var confirm = $mdDialog.confirm()
                .title('Atenção!')
                .textContent('Deseja validar o Relatório de Fiscalização?')
                .ok('Confirmar')
                .cancel('Cancelar');

            $mdDialog.show(confirm).then(function () {
                service.validar(registro.id).then(function () {
                    $state.go('fiscalizacao.relatorio.listar');
                });
            });
        }
        };

        /**
         * Adiciona um novo relatório fotográfico a lista de relatórios fotográficos
         * @param files
         */
        scope.adicionarRelatorioFotografico = function (files) {

            if (files && files.length) {
                for (var i = 0; i < files.length; i++) {
                    ArquivoServiceFiscalizacao.uploadArquivo(files[i]).then(function (response) {
                        if (!scope._registro.relatoriosFotograficos) {
                            scope._registro.relatoriosFotograficos = [];
                        }

                        scope._registro.relatoriosFotograficos.push(response.data);
                    });
                }
            }
        };

        /**
         * Exclui o relatório fotográfico selecionado pelo usuário
         * @param index
         */
        scope.excluirRelatorioFotografico = function (index) {
            scope._registro.relatoriosFotograficos.splice(index, 1);
        };

        /**
         * Obtém uma lista de tipos de logradouro
         */
        scope.getTipoLogradouro = function (callback) {
            scope._tipoLogradouro = {};
            service.getTipoLogradouro().then(function (objectReturn) {
                scope._tipoLogradouro = objectReturn.data;

                if(callback){
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de municípios
         * @param sigla
         * @param callback
         */
        scope.getMunicipios = function (sigla, callback) {

            scope._municipios = [];
            if(scope.alteracaoManual && scope._registro.endereco)
                scope._registro.endereco.municipio = null;
            scope.alteracaoManual = true;

            if (sigla) {
                integradorService.getMunicipios(sigla).then(function (objectReturn) {
                    scope._municipios = $filter("orderbyAccentsName")(objectReturn.data);
                    if (callback) {
                        callback();
                    }
                });
            }
		};

		scope.getMunicipiosPesquisaMapa = function (sigla) {
			scope._municipiosMapa = [];
            if (sigla) {
                integradorService.getMunicipios(sigla).then(function (objectReturn) {
                    scope._municipiosMapa = $filter("orderbyAccentsName")(objectReturn.data);
                });
            }
        };

		scope.onChangeMunicipioPesquisa = function (estado, municipio) {

			CartografiaServiceFiscalizacao.buscarGeometria({uf: estado, municipio: municipio.nome}).then(function (result) {
				if (result.data) {
					NotificadorServiceFiscalizacao.notify($scope, result.data, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.CENTRALIZAR_POR_MUNICIPIO);
				}
			});
		}
        /**
         * Obtém a entidade do usuário logado
         * @param callback
         */
        scope.getUsuario = function (callback) {
            scope._usuario = {};
            service.getUsuario().then(function (objectReturn) {
                scope._usuario = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Verifica se o usuário possui jurisdição no estado retornado pela consulta do cep.
         * @returns {*}
         */



        function validaJurisdicaoCep(ufCep) {
            if (scope._usuario.jurisdicoes && scope._usuario.jurisdicoes.length > 0) {
                for(var i = 0; i < scope._usuario.jurisdicoes.length; i++){
                    if(scope._usuario.jurisdicoes[i] === ufCep && scope._usuario.jurisdicoes[i] === scope._registro.demanda.endereco.estado){
                        return true;
                    }
                }

                return false;
            } else {
                return false;
            }
        }

        /**
         * Obtém uma lista de tipos de documento
         * @param callback
         */
        scope.getTiposDocumento = function (callback) {
            service.getTiposDocumento().then(function (objectReturn) {
                scope._tiposDocumento = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de ordens de fiscalização
         * @param callback
         */
        scope.getOrdensFiscalizacao = function(callback){
            service.getOrdensFiscalizacaoPorJurisdicao().then(function (objectReturn) {
                scope._ordemFiscalizacao = angular.copy(objectReturn.data);
                console.log(scope._ordemFiscalizacao);

                if(callback){
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de status
         * @param callback
         */
        scope.getStatus = function(callback){
            service.getStatus().then(function (objectReturn) {
                scope._status = angular.copy(objectReturn.data);

                if(callback){
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de motivações
         * @param callback
         */
        scope.getMotivacoes = function (callback) {
            scope._motivacoes = {};
            service.getMotivacoes().then(function (objectReturn) {
                scope._motivacoes = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de assuntos
         * @param callback
         */
        scope.getAssuntos = function (callback) {
            scope._assuntos = {};
            service.getAssuntos().then(function (objectReturn) {
                scope._assuntos = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de tipos de imóvel
         * @param callback
         */
        scope.getTiposImovel = function (callback) {
            scope._tiposImovel = {};
            service.getTiposImovel().then(function (objectReturn) {
                scope._tiposImovel = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de zoneamentos
         * @param callback
         */
        scope.getZoneamentos = function (callback) {
            scope._zoneamentos = {};
            service.getZoneamentos().then(function (objectReturn) {
                scope._zoneamentos = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém o enderço pelo cep
         * @param cep
         */
        scope.getCEP = function (cep) {
            if (cep && cep.length === 8) {
                scope._endereco = {};
                integradorService.getCEP(cep).then(function (objectReturn) {
                    if (!objectReturn.data) {
                        ComumFactory.mostrarMensagemError('CEP não encontrado!');
                        return;
                    }
                    if (!validaJurisdicaoCep(objectReturn.data.uf)) {
                        ComumFactory.mostrarMensagemError('O CEP informado não pertence a sua Unidade Federativa!');
                        scope._registro.endereco.cep = "";
                        return;
                    }

                    var retorno = objectReturn.data;
                    scope._registro.endereco.logradouro = retorno.logradouro;

                    for (var obj in scope._tipoLogradouro) {
                        if (scope._tipoLogradouro[obj] === retorno.tipoLogradouro) {
                            scope._registro.endereco.tipoLogradouro = obj;
                        }
                    }

                    scope._registro.endereco.bairro = retorno.bairro;

                    for (var i = 0; i < scope._usuario.jurisdicoes.length; i++) {
                        if (scope._usuario.jurisdicoes[i] === retorno.uf) {
                            scope._registro.endereco.estado = scope._usuario.jurisdicoes[i];
                            scope.getMunicipios((scope._registro.endereco.estado), function() {
                                for (var i = 0; i < scope._municipios.length; i++) {
                                    if (scope._municipios[i].codigo === retorno.codMunicipio) {
                                        scope._registro.endereco.municipio = scope._municipios[i];
                                        break;
                                    }
                                }
                            });
                            break;
                        }
                    }

                }, function () {
                    ComumFactory.mostrarMensagemError('CEP não encontrado!');
                });
            } else {
                ComumFactory.mostrarMensagemError('Informe um CEP válido!');
            }
        };

        function dataURItoBlob(dataURI) {
            var byteString;
            if (dataURI.split(',')[0].indexOf('base64') >= 0)
                byteString = atob(dataURI.split(',')[1]);
            else
                byteString = unescape(dataURI.split(',')[1]);

            var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

            var ia = new Uint8Array(byteString.length);
            for (var i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }

            return new Blob([ia], {type: mimeString});
        }

        /**
         * Verifica a opção selecionada da ocorrência de infração é false
         * @param opt
         */
        scope.handleOcorrenciaDeInfracao = function (ocorrenciaDeInfracao) {
            ocorrenciaDeInfracao === false ? scope.verificaOcorrenciaInfracao() : scope.iniciaAutuado();
        };

        /**
         * Caso ocorra uma Infração, a entidade autuado será inicializada.
         */
        scope.iniciaAutuado = function(){
            scope._autuado = {};
            scope._autuado.responsavel = {};
            scope._autuado.responsavel.cpfCnpj = null;
            scope._autuado.responsavel.nome = null;
            scope._autuado.processoSEI = null;
            scope._autuado.processoAdministrativo = false;
            scope._autuado.importarResponsavel = false;
        };

        /**
         * Limpar as variáves referentes a ocorrencia de infração
         */
        scope.verificaOcorrenciaInfracao = function () {
            scope._autuado = null;
            scope._registro.autuados && scope._registro.autuados.length ? scope._registro.autuados = [] : "";
            scope.showAreaAutuada === true ? scope.showAreaAutuada = false : '';

            if (scope._registro.tipoImovel && scope._registro.tipoImovel.tipo) {
                scope.mostrarOcorrenciaInfracao = true;
                scope._registro.ocorrenciaDeInfracao = false;

                !scope._registro.confirmaResponsavel && scope._registro.tipoImovel && scope._registro.tipoImovel.tipo ? scope.obrigatoriedadeOcupante = true : scope.obrigatoriedadeOcupante = false;
            }
        };

        /**
         * Valida se o ponto a ser marcado no mapa pertence a jurisdição do usuário
         * @param imovel
         */
        scope.validaPonto = function (imovel) {
            integradorService.validar(imovel).then(function (objectResponse) {
                if (objectResponse.data.noPais === 'Brasil' && !validaJurisdicao(objectResponse.data.sgUf)) {
                    NotificadorServiceFiscalizacao.notify(scope, imovel, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.MARCAR_PONTO_POR_COORDENADAS);
                } else {
                    $timeout(function () {
                        var endereco = {};
                        endereco.coordinates = [];
                        endereco.coordinates[0] = null;
                        endereco.coordinates[1] = null;

                        var object = null;

                        NotificadorServiceFiscalizacao.notify(object, endereco.coordinates, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.ATUALIZAR_COORDENADAS_DO_PONTO);
                        NotificadorServiceFiscalizacao.notify(object, endereco, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.MARCAR_PONTO_POR_COORDENADAS);
                    });

                    ComumFactory.mostrarMensagemError('Área selecionada não pertence a sua UF');
                }
            });
        };

        /**
         * Valida se o poligono a ser marcado no mapa pertence a jurisdição do usuário
         * @param imovel
         */
        scope.validaPoligono = function (imovel) {
            integradorService.validar(imovel).then(function (objectResponse) {
                if (!objectResponse.data.noPais === 'Brasil' && !validaJurisdicao(objectResponse.data.sgUf)) {
                    $timeout(function () {
                        scope._registro.croqui = null;
                        NotificadorServiceFiscalizacao.notify(null, null, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.APAGAR_BENFEITORIA_POR_COORDENADAS);
                    });

                    ComumFactory.mostrarMensagemError('Área selecionada não pertence a sua UF');
                }
            });
        };

        /**
         * Obtém a lista de jurisdições do usuário
         * @returns {*}
         */
        function validaJurisdicao(ufMapa) {
            var demandaUf = scope._registro.demanda.endereco.estado;
            if (scope._usuario.jurisdicoes && scope._usuario.jurisdicoes.length > 0) {
                for (var i = 0; i < scope._usuario.jurisdicoes.length; i++) {
                    if(scope._usuario.jurisdicoes[i] === ufMapa && scope._usuario.jurisdicoes[i] === demandaUf){
                        return true;
                    }
                }

                return false;
            }else{
                return false;
            }
        }

        /**
         * Avança para a próxima tab
         */
        scope.nextTab = function () {
            scope.apenasLeitura = true;
            $scope.tabIndex++;
            scope.apenasLeitura = false;
        };

        /**
         * Retorna a tab anterior
         */
        scope.previousTab = function () {
            $scope.tabIndex--;
        };
        /**
         * Verfica se o autuando sendo cadastrado ja consta na lista
         */
        scope.verificaAutuadoLista = function (autuado) {
            if (scope._registro.autuados.length == 0) {
                scope.servicoAdicionarNovoAutuado(autuado);
            } else {
                var constaLista = false;
                for (var i = 0; i < scope._registro.autuados.length; i++) {
                    if (autuado.responsavel.cpfCnpj == scope._registro.autuados[i].responsavel.cpfCnpj) {
                        constaLista = true;
                        ComumFactory.mostrarMensagemError('Este autuado já foi adicionado ao relatório');
                    }
                }

                if (!constaLista) {
                    scope.servicoAdicionarNovoAutuado(autuado);
                }
            }
        };

        /**
         * Adiciona um novo autuado
         * @param autuado
         */
        scope.adicionarNovoAutuado = function(autuado){
            if (scope._autuado.autuadoNaoIdentificado == false || scope._autuado.autuadoNaoIdentificado == undefined) {
                scope.verificaAutuadoLista(autuado);
            } else {
                autuado.responsavel = {};
                autuado.responsavel.cpfCnpj = "-";
                autuado.responsavel.nome = "Autuado não identificado"
                autuado.processoSEI = "";
                autuado.processoAdministrativo = "";

                scope.setStatusAutuado(autuado);
                scope._registro.autuados.push(angular.copy(autuado));
                scope.iniciaAutuado();
            }
        };

        scope.servicoAdicionarNovoAutuado = function (autuado) {
            if (!autuado.responsavel.nome || !autuado.responsavel.cpfCnpj || (!autuado.processoSEI && !autuado.processoAdministrativo)) {
                scope.validaFormAutuado(autuado);
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
            } else {
                if (autuado.processoAdministrativo) {
                    service.autuadoProcessoSei(autuado).then(function (response) {
                        autuado = response.data;
                        scope.setStatusAutuado(autuado);
                        scope._registro.autuados.push(angular.copy(autuado));
                        scope.iniciaAutuado();
                    });
                } else {
                    scope.setStatusAutuado(autuado);
                    scope._registro.autuados.push(angular.copy(autuado));
                    scope.iniciaAutuado();
                }
            }
        }

        scope.validaFormAutuado = function(autuado){
            autuado.responsavel.nome && autuado.responsavel.nome.length > 0 ? scope.autuadoNomeInvalido = false : scope.autuadoNomeInvalido = true;
            autuado.responsavel.cpfCnpj && autuado.responsavel.cpfCnpj.length > 0 ? scope.autuadoCpfCnpjInvalido = false : scope.autuadoCpfCnpjInvalido = true;
            autuado.processoSEI && autuado.processoSEI.length > 0 ? scope.autuadoProcessoSeiInvalido = false : scope.autuadoProcessoSeiInvalido = true;
            autuado.processoAdministrativo ? scope.autuadoProcessoSeiInvalido = false : scope.autuadoProcessoSeiInvalido = true;
        };

        /**
         * Seta o status do autuado (Autuado ou Responsável)
         * @param autuado
         */
        scope.setStatusAutuado = function(autuado){
            autuado.importarResponsavel === true ? autuado.status = "Responsável" : autuado.status = "Autuado";
        };

        /**
         * Veririfica se exites um responsável cadastrado (demanda ou relatório ordem fiscalização) e o;
         * importa como autuado.
         * @param relatorioOrdemFiscalizacao
         */
        scope.importarResponsavel = function(relatorioOrdemFiscalizacao){
            if(scope._autuado.importarResponsavel){
                if(relatorioOrdemFiscalizacao.confirmaResponsavel){
                    if(relatorioOrdemFiscalizacao.demanda.responsavel){
                        scope._autuado.responsavel = angular.copy(relatorioOrdemFiscalizacao.demanda.responsavel);
                    }else{
                        ComumFactory.mostrarMensagemError('Nenhum responsável cadastrado!');
                        scope._autuado.importarResponsavel = false;
                    }
                }else{
                    if(relatorioOrdemFiscalizacao.responsavel){
                        scope._autuado.responsavel = angular.copy(relatorioOrdemFiscalizacao.responsavel);
                    }else{
                        ComumFactory.mostrarMensagemError('Nenhum responsável cadastrado!');
                        scope._autuado.importarResponsavel = false;
                    }
                }

                scope.validaFormAutuado(scope._autuado);
            }else{
                scope._autuado.responsavel.nome = null;
                scope._autuado.responsavel.cpfCnpj = null;
                scope._autuado.status = null;

                scope.validaFormAutuado(scope._autuado);
            }
        };

        /**
         *Caso o campo do processo SEI esteja preenchido e o usuário marcar o campo de processoAdministrativo,
         * o processoSEI será limpado
         */
        scope.validarProcessoSEI = function(processoAdministrativo){
            processoAdministrativo ? scope._autuado.processoSEI = null : "";

            scope.validaFormAutuado(scope._autuado);
        };

        scope.getDocumentos = function(registro){
            for(var i = 0; i < registro.autuados.length; i++){
                for(var y = 0; y < registro.autuados[i].documentos.length; y++){
                    if(registro.autuados[i].documentos[y].status !== 'AUTO_EXCLUIDO'){
                        scope._documentos.push(registro.autuados[i].documentos[y])
                    }
                }
            }
        };

        scope.verificaDocumentoPendente = function(registro){
            for(var i = 0; i < registro.autuados.length; i++){

                for(var y = 0; y < registro.autuados[i].documentos.length; y++){
                    if(registro.autuados[i].documentos[y].status === 'AUTO_EXCLUIDO'){
                        continue;
                    }

                    if(registro.autuados[i].documentos[y].statusDocumentoSei == null || registro.autuados[i].documentos[y].statusDocumentoSei !== 'AUTUADO'){
                        scope.documentoPendente = true;
                        break;
                    }else{
                        scope.documentoPendente = false;
                    }

                }
            }
        };

        scope.excluirRegistro = function (registro) {
            var confirm = $mdDialog.confirm()
                .title('Atenção!')
                .textContent('Tem certeza de que deseja remover este autuado?')
                .ok('Confirmar')
                .cancel('Cancelar');

            $mdDialog.show(confirm).then(function () {
                var index = scope._registro.autuados.indexOf(registro);
                scope._registro.autuados.splice(index, 1);
                ComumFactory.mostrarMensagemSucesso('Autuado removido.');
            });
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao')
        .factory('TotalSancaoService', ["$http", function ($http) {
            var baseUrl = '/fiscalizacao/api/painel-sancao';

            var getGraficoNotificacaoByFilter = function (filtro) {
                return $http.post(baseUrl + '/grafico-notificacao-filter', filtro);
            };

            var getGraficoInfracaoByFilter = function (filtro) {
                return $http.post(baseUrl + '/grafico-infracao-filter', filtro);
            };

            var getUsuario = function () {
                return $http.get(baseUrl + '/usuario');
            };

            var getTiposDocumento = function () {
                return $http.get(baseUrl + '/tipos-documento');
            };

            var getTotalDemandas = function (filtro) {
                return $http.post(baseUrl + '/quantidade-demanda', filtro);
            };

            var getTotalDocumentosByFilter = function (filtro) {
                return $http.post(baseUrl + '/quantidade-documento-filter', filtro);
            };

            var getQuantidadePrazo = function (filtro) {
                return $http.post(baseUrl + '/quantidade-pendente-entrega', filtro);
            };

            return {
                getGraficoNotificacaoByFilter: getGraficoNotificacaoByFilter,
                getGraficoInfracaoByFilter: getGraficoInfracaoByFilter,
                getUsuario: getUsuario,
                getTiposDocumento: getTiposDocumento,
                getTotalDemandas: getTotalDemandas,
                getTotalDocumentosByFilter: getTotalDocumentosByFilter,
                getQuantidadePrazo:getQuantidadePrazo
            };
        }]);
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('TotalSancaoController', ['$filter', '$scope', '$rootScope', '$state', '$stateParams', '$timeout', 'TotalSancaoService', 'ComumFactory', 'IntegradorService', controller]);

    function controller($filter, $scope, $rootScope, $state, $stateParams, $timeout, service, ComumFactory, IntegradorService) {
        var scope = this;

        scope.init = function () {
            scope.apenasLeitura = true;
            scope.quantidadePrazoNotificacao = false;
            scope.quantidadePrazoInfracao = false;

            scope.toolTipoPendenteEntrega = "10 dias após emissão inicial;\n10 dias apoś análise de recurso";
            scope.recursoInstancia = "Entre os dias 10 e 30 após o recebimento da sanção";
            scope.segundaInstanciaRecurso = "Entre os dias 10 e 30 após o recebimento so recurso em primeira instância";
            scope.recurso = "30 dias após o recebimento do recurso";
            scope.reconsideracao = "5 dias após do recebimento do recurso em 2° instância";

            scope.getUsuario(function () {
                scope.getEstados(function () {
                    scope.getTiposDocumento(function () {
                        scope.limparFiltros(function(){
                            scope.apenasLeitura = false;
                        }) ;
                    });
                });
            });
        };

        scope.filtrar = function(filtros){
            if($scope.formRegistro.$valid){
                var filtro = filtros;

                scope.limpaCampos();

                scope.ufs =  filtro.uf.toString();

                var isNotificacao = false;
                var isInfracao = false;

    
                filtro.tipoDocumento.forEach(function (tipoDocumento) {
                    if(tipoDocumento === 'NOTIFICACAO'){
                        isNotificacao = true;
                       
                    } else if(tipoDocumento === 'AUTO_DE_INFRACAO'){
                        isInfracao = true;
                    }
                });

                if(isNotificacao){
                    scope.getGraficoNotificacaoByFilter(filtro, function(){
                            scope.apenasLeitura = false;
                    });
                }
                

                if(isInfracao) {
                    scope.getGraficoInfracaoByFilter(filtro, function () {
                            scope.apenasLeitura = false;
                    });
                }

                
                scope.getQuantidadeDemanda(filtro, function () {
                    scope.getQuantidadeDocumentoByFilter(filtro, function(){
                        scope.getQuantidadePrazo(filtro, function(){
                            scope.apenasLeitura = false;
                        });
                    });
                });
                
                scope.permitirAcao = true;
            }
        };

        scope.limpaCampos = function(callback){
            
            scope.apenasLeitura = true;
            scope.quantidadePrazoNotificacao = false;
            scope.quantidadePrazoInfracao = false;
            scope._infracao = {
                data: [],
                labels: [],
                series: [],
                colors: [
                    '#5A9AD5',
                    '#EC7D31',
                    '#A3A3A3',
                    '#FFC000',
                    '#4371C3',
                    '#6EAC45'
                ]
            };
            scope._notificacao = {
                data: [],
                labels: [],
                series: [],
                colors: [
                    '#5A9AD5',
                    '#EC7D31',
                    '#A3A3A3',
                    '#FFC000',
                    '#4371C3',
                    '#6EAC45'
                ]
            };
            scope.ufs = [];

            if(callback){
                callback();
            }
        };

        scope.getGraficoNotificacaoByFilter = function (filtro, callback) {
            service.getGraficoNotificacaoByFilter(filtro).then(function (objectReturn) {
                scope._notificacao = {
                    data: [],
                    labels: [],
                    series: [],
                    colors: [
                        '#5A9AD5',
                        '#EC7D31',
                        '#A3A3A3',
                        '#FFC000',
                        '#4371C3',
                        '#6EAC45'
                    ], 
                    options:{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                            labels: {
                                render: "percentage",
                                precision: 0,
                                showActualPercentages: true,
                                fontSize: 12,
                                fontColor: "#000000",
                                fontStyle: "bold",
                              
                            }
                        }
                    }
                };

                for (var i = 0; i < objectReturn.data.length; i++) {
                    var data = objectReturn.data[i];

                    scope._notificacao.data.push(data.quantidade);
                    scope._notificacao.labels.push(data.nome);
                    scope._notificacao.series.push(data.nome);
                }

                if(callback){
                    callback();
                }
            });
        };

        scope.getGraficoInfracaoByFilter = function (filtro, callback) {
            service.getGraficoInfracaoByFilter(filtro).then(function (objectReturn) {
                scope._infracao = {
                    data: [],
                    labels: [],
                    series: [],
                    colors: [
                        '#5A9AD5',
                        '#EC7D31',
                        '#A3A3A3',
                        '#FFC000',
                        '#4371C3',
                        '#6EAC45',
                        '#CB6ECC',
                        '#4682B4',
                        '#CD5C5C',
                        '#D3D3D3',
                        '#FF69B4',
                        '#7FFFD4',
                        '#FFFF00',
                        '#7CFC00',
                        '#B22222'
                    ],
                    options:{
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                            labels: {
                                render: "percentage",
                                precision: 0,
                                showActualPercentages: true,
                                fontSize: 12,
                                fontColor: "#000000",
                                fontStyle: "bold",
                              
                            }
                        }
                    }
                };

                for (var i = 0; i < objectReturn.data.length; i++) {
                    var data = objectReturn.data[i];

                    scope._infracao.data.push(data.quantidade);
                    scope._infracao.labels.push(data.nome);
                    scope._infracao.series.push(data.nome);
                }

                if(callback){
                    callback();
                }
            });
        };

        scope.limparFiltros = function (callback) {
            scope.apenasLeitura = true;

            if ($scope.formRegistro) {
                $scope.formRegistro.$setUntouched();
                $scope.formRegistro.$setPristine();
            }
            scope.permitirAcao = false;

            var d = new Date();
            var ano = d.getFullYear();
            var dataInicial = new Date('01/01/' + ano);
            var dataFinal = new Date('12/31/' + ano);

            scope._filtros = {};
            scope._filtros.dataInicio = dataInicial;
            scope._filtros.dataFim = dataFinal;
            scope._filtros.uf = [];
            scope._filtros.tipoDocumento = [];

            if (callback) {
                callback();
            }

        };

        scope.getEstados = function (callback) {
            scope._estados = [];
            IntegradorService.getEstados().then(function (objectReturn) {
                objectReturn.data.forEach(function (estado) {
                    scope._usuario.jurisdicoes.forEach(function (juridicao) {
                        if (juridicao === estado.sigla) {
                            scope._estados.push(estado);
                        }
                    });
                });    

      
                if (callback) {
                    callback();
                }
            });
        };

        scope.getUsuario = function (callback) {
            scope._usuario = {};
            service.getUsuario().then(function (objectReturn) {
                scope._usuario = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.getTiposDocumento = function(callback){
            service.getTiposDocumento().then(function(objectResponse){
                scope._tiposDocumento = objectResponse.data;

                if(callback){
                    callback();
                }
            })
        };

        scope.getQuantidadeDemanda = function(filtro, callback){
            service.getTotalDemandas(filtro).then(function(objectResponse){
                scope.quantidadeDemanda = objectResponse.data;

                if(callback){
                    callback();
                }
            });
        };

        scope.getQuantidadeDocumentoByFilter = function(filtro, callback){
            service.getTotalDocumentosByFilter(filtro).then(function(objectResponse){
                    scope.quantidadeDocumento = objectResponse.data;
                if(callback){
                    callback();
                }
            });
        };

        scope.getQuantidadePrazo = function(filtro, callback){
            service.getQuantidadePrazo(filtro).then(function(objectResponse){
                scope.quantidadePrazo = objectResponse.data;

                if( scope.quantidadePrazo.qtdPendenteEntregaNotificacao || scope.quantidadePrazo.qtdVencidoPrimeiraInstanciaNotificacao ||
                    scope.quantidadePrazo.qtdVencidoSegundaInstanciaNotificacao || scope.quantidadePrazo.qtdRecursoVencidoPrimeiraInstanciaNotificacao
                    || scope.quantidadePrazo.qtdRecursoVencidoSegundaInstanciaNotificacao || scope.quantidadePrazo.qtdReconsideracaoVencidoNotificacao){
                        scope.quantidadePrazoNotificacao = true;
                    }

                if( scope.quantidadePrazo.qtdPendenteEntregaInfracao || scope.quantidadePrazo.qtdVencidoPrimeiraInstanciaInfracao ||
                    scope.quantidadePrazo.qtdVencidoSegundaInstanciaInfracao || scope.quantidadePrazo.qtdRecursoVencidoPrimeiraInstanciaInfracao
                    || scope.quantidadePrazo.qtdRecursoVencidoSegundaInstanciaInfracao || scope.quantidadePrazo.qtdReconsideracaoVencidoInfracao){
                            scope.quantidadePrazoInfracao = true;
                        }

                if(callback){
                    callback();
                }
            });
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao')
        .factory('TotalFiscalizacaoService', ["$http", function ($http) {
            var baseUrl = '/fiscalizacao/api/total-fiscalizacao';

            var getTotalFiscalizacao = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl, request);
            };

            var gerarCsv = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/csv', request);
            };

            return {
                getTotalFiscalizacao: getTotalFiscalizacao,
                gerarCsv: gerarCsv
            };
        }]);
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('TotalFiscalizacaoController', ['$filter', '$scope', '$rootScope', '$state', '$stateParams', '$timeout', 'TotalFiscalizacaoService', 'ComumFactory', 'IntegradorService', '$mdDialog', 'ciMapaLayersFactoryFiscalizacao', 'ciMapaViewFactoryFiscalizacao', 'NotificadorServiceFiscalizacao', 'MAPA_CONSTANTS_FISCALIZACAO', controller]);

    function controller($filter, $scope, $rootScope, $state, $stateParams, $timeout, service, ComumFactory, IntegradorService, $mdDialog, ciMapaLayersFactoryFiscalizacao, ciMapaViewFactoryFiscalizacao, NotificadorServiceFiscalizacao, MAPA_CONSTANTS_FISCALIZACAO) {
        var scope = this;

        scope.image = {};
        scope.labels = [];
        scope.series = ['Metas Executadas', 'Metas Previstas'];
        scope.colors = ['#6696C5', '#D88850'];
        scope.data = [];

        scope.permitirAcao = false;
        scope.showTabela = true;
        scope.showChart = true;

        scope._tabela = [];
        scope.total = {};

        scope.init = function () {
            scope.limparFiltros();
            scope.iniciarMapa();
        };

        scope.iniciarMapa = function (callback) {
            ciMapaLayersFactoryFiscalizacao.init();
            ciMapaViewFactoryFiscalizacao.init();

            if (callback) {
                callback();
            }
        };


        scope.limparFiltros = function () {
            if ($scope.formRegistro) {
                $scope.formRegistro.$setUntouched();
                $scope.formRegistro.$setPristine();
            }
            scope.permitirAcao = false;

            scope.filtros = {};
            scope.filtros.dtInicio = '';
            scope.filtros.dtFim = '';

            scope._tabela.length > 0 ? scope._tabela = [] : '';
            scope.labels.length > 0 ? scope.labels = [] : '';
            scope.data.length > 0 ? scope.data = [] : '';
        };

        scope.filtrar = function (filtro) {
            $scope.formRegistro.$setSubmitted();
            if ($scope.formRegistro.$valid) {
                scope.labels = [];
                scope.data = [];
                service.getTotalFiscalizacao(filtro).then(function (objectReturn) {
                    var metas = angular.copy(objectReturn.data);

                    if (metas.length) {
                        var metasExecutadas = [];
                        var metasPrevistas = [];

                        var totalPrevista = 0;
                        var totalExecutada = 0;

                        for (var j = 0; j < metas.length; j++) {
                            var meta = metas[j];

                            scope.labels.push(meta.siglaUf);

                            metasExecutadas.push(meta.metaExecutada);
                            metasPrevistas.push(meta.metaPrevista);

                            scope._tabela.push({
                                uf: meta.siglaUf,
                                planejado: meta.metaPrevista,
                                executado: meta.metaExecutada,
                                porcentagem: (meta.metaExecutada / meta.metaPrevista) * 100
                            });

                            totalPrevista += meta.metaPrevista;
                            totalExecutada += meta.metaExecutada;
                        }

                        scope.total = {
                            uf: "TOTAL",
                            planejado: totalPrevista,
                            executado: totalExecutada,
                            porcentagem: (totalExecutada / totalPrevista) * 100
                        };

                        scope.data.push(metasExecutadas, metasPrevistas);
                    } else {
                        scope.carregaGraficoLimpo();
                    }

                    scope.permitirAcao = true;
                });

            } else {
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
            }
        };

        scope.carregaGraficoLimpo = function () {
            scope.getEstados(function () {

                for (var i = 0; i < scope._estados.length; i++) {
                    scope.labels.push(scope._estados[i].sigla);

                    scope.data.push(0);
                }
            });
        };

        scope.getEstados = function (callback) {
            scope._estados = [];
            IntegradorService.getEstados().then(function (objectReturn) {
                scope._estados = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.showAlert = function (ev) {
            $mdDialog.show({
                controller: function () {
                    return scope;
                },
                controllerAs: 'scope',
                templateUrl: 'scripts-fiscalizacao/pages/painel/modalTemplate/modalTemplate.tmpl.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose: true,
                fullscreen: true
            });
        };

        scope.cancelModal = function (callback) {
            $mdDialog.cancel();

            if (callback) {
                callback();
            }
        };

        scope.gerarPng = function (callback) {
            $timeout(function(){
                scope.gerarChartPng(function(){
                    $timeout(function(){
                        scope.gerarTablePng(function(){

                        });

                    }, 500);
                });
            },500);

            if(callback){
                callback();
            }
        };

        scope.gerarChartPng = function(callback){
            domtoimage.toPng(document.getElementById('chart')).then(function (dataUrl) {
                var link = document.createElement('a');
                scope.chartImage = angular.copy(dataUrl);
                link.download = 'TOTAL_FISCALIZACAO_UF.png';
                link.href = dataUrl;
                link.click();
            });
            if(callback){
                callback();
            }
        };

        scope.gerarTablePng = function(callback){
            domtoimage.toPng(document.getElementById('table')).then(function (dataUrl) {
                    var link = document.createElement('a');
                    scope.tableImage = angular.copy(dataUrl);
                    link.download = 'TOTAL_FISCALIZACAO_UF_TABELA.png';
                    link.href = dataUrl;
                    link.click();
                });
            if(callback){
                callback();
            }
        };

        scope.pdfJsonGenerator = function () {
            var pdf = {
                content: [
                    {
                        style: 'table',

                        table: {
                            widths: ['*'],
                            body: [
                                [

                                ]
                            ]
                        },

                        layout: 'noBorders'
                    },
                    {
                        style: 'table',
                        table: {
                            widths: ['*', '*', '*', '*'],
                            body: []
                        },
                        layout: {
                            hLineWidth: function (i, node) {
                                return (i === 0 || i === node.table.body.length) ? 0.2 : 0.2;
                            },
                            vLineWidth: function (i, node) {
                                return (i === 0 || i === node.table.widths.length) ? 0 : 0;
                            },
                            hLineColor: function (i, node) {
                                return (i === 0 || i === node.table.body.length) ? '#c0c0c0' : '#c0c0c0';
                            },
                            vLineColor: function (i, node) {
                                return (i === 0 || i === node.table.widths.length) ? '#c0c0c0' : '#c0c0c0';
                            }
                        }
                    },
                    {
                        style: 'table',

                        table: {
                            widths: [550],
                            body: [
                                [
                                    {image: scope.chartImage, width: 550, margin: [0, 0, 0, 10]}
                                ]
                            ]
                        },

                        layout: 'noBorders'
                    }
                ],
                styles: {
                    titleMargin: {
                        margin: [5, 17, 18, 0]
                    },
                    imageMargin: {
                        margin: [0, 15, 5, 0]
                    },
                    green: {
                        fillColor: '#4CAF50'
                    },
                    red: {
                        fillColor: '#FD7D81'
                    },
                    table: {
                        margin: [10, 11, 20, 1]
                    },
                    headerColor:{
                        fillColor: '#ECECEC'
                    }

                },
                pageSize: 'A4',
                pageOrientation: 'portrait',
                // [left, top, right, bottom] or [horizontal, vertical] or just a number for equal margins
                pageMargins: [20, 20]
            };

            var header = scope.addHeader();
            var dados = scope.addDados();

            pdf.content[0].table.body = header;
            pdf.content[1].table.body = dados;

            return pdf;
        };

        scope.addHeader = function () {
            var today = new Date();
            var year = today.getFullYear();
            var month = today.getMonth() + 1;
            if (month < 10) {
                month = '0' + month;
            }
            var day = today.getDate();
            if (day < 10) {
                day = '0' + day;
            }
            var fullYear = day + '/' + month + '/' + year;


            var body = [];

            body.push([
                {text: fullYear, bold: false, fontSize: 10, alignment: 'right'}
            ]);

            body.push([
                {text: 'Relatório de fiscalizações executadas por mês', bold: true, fontSize: 10, alignment: 'center', margin: [0, 10, 0, 10]}
            ]);



            return body;
        };

        scope.addDados = function () {
            var body = [];
            var itens = angular.copy(scope._tabela);

            body.push([
                {text: 'UF', bold: true, fontSize: 10, alignment: 'center', style: 'headerColor', margin: [0, 5, 0, 5]},
                {text: 'Planejado', bold: true, fontSize: 10, alignment: 'center', style: 'headerColor', margin: [0, 5, 0, 5]},
                {text: 'Executado', bold: true, fontSize: 10, alignment: 'center', style: 'headerColor', margin: [0, 5, 0, 5]},
                {text: 'Porcentagem', bold: true, fontSize: 10, alignment: 'center', style: 'headerColor', margin: [0, 5, 0, 5]}
            ]);

            for (var i = 0; i < itens.length; i++) {
                if(!itens[i].porcentagem){
                    itens[i].porcentagem = 0;
                    itens[i].porcentagem = itens[i].porcentagem.toString();
                    itens[i].red = true;
                }else{
                    itens[i].porcentagem = $filter('porcentagem')(itens[i].porcentagem);
                    if(itens[i].porcentagem > 50){
                        itens[i].red = false;
                    }else{
                        itens[i].red = true;
                    }
                    itens[i].porcentagem.toString();
                }

                var column = [];
                column.push(
                    {text: itens[i].uf, fontSize: 10, alignment: 'center', style: itens[i].red === true ? 'red' : '', margin: [0, 4, 0, 4]},
                    {text: itens[i].planejado, fontSize: 10, alignment: 'right', style: itens[i].red === true ? 'red' : '', margin: [0, 4, 0, 4]},
                    {text: itens[i].executado, fontSize: 10, alignment: 'right', style: itens[i].red === true ? 'red' : '', margin: [0, 4, 0, 4]},
                    {text: itens[i].porcentagem + '%', fontSize: 10, alignment: 'center', style: itens[i].red === true ? 'red' : '', margin: [0, 4, 0, 4]}
                );

                body.push(column);
            }


            var total = angular.copy(scope.total);
            total.porcentagem = $filter('porcentagem')(total.porcentagem);
            body.push([
                {text: 'Total:', bold: true, fontSize: 10, alignment: 'center', style: 'headerColor', margin: [0, 4, 0, 5]},
                {text: total.planejado, bold: false, fontSize: 10, alignment: 'right', style: 'headerColor', margin: [0, 5, 0, 5]},
                {text: total.executado, bold: false, fontSize: 10, alignment: 'right', style: 'headerColor', margin: [0, 5, 0, 5]},
                {text: total.porcentagem  + "%", bold: false, fontSize: 10, alignment: 'center', style: 'headerColor', margin: [0, 5, 0, 5]}
            ]);

            return body;
        };

        scope.downloadPDF = function () {
            domtoimage.toPng(document.getElementById('chart')).then(function (dataUrl) {
                scope.chartImage = angular.copy(dataUrl);
                pdfMake.createPdf(scope.pdfJsonGenerator()).download('TOTAL_FISCALIZACAO_UF.pdf', function () {
                    $scope.$apply();
                });
            });
        };

        scope.printPdf = function () {
            domtoimage.toPng(document.getElementById('chart')).then(function (dataUrl) {
                scope.chartImage = angular.copy(dataUrl);
                pdfMake.createPdf(scope.pdfJsonGenerator()).print({});
                $scope.$apply();
            });
        };

        scope.gerarCsv = function () {
            service.gerarCsv(scope.filtros).then(function(resp){

                var csvFile = new Blob([resp.data], { type: "text/csv" });
                var downloadLink = document.createElement("a");
                downloadLink.download = 'totalFiscalizacao.csv';
                downloadLink.href = window.URL.createObjectURL(csvFile);
                downloadLink.style.display = "none";
                document.body.appendChild(downloadLink);
                downloadLink.click();
            });

        };

        scope.gerarExcel = function () {

        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao')
        .factory('PainelGerencialService', ["$http", function ($http) {
            var baseUrl = '/fiscalizacao/api/painel-gerencial';

            var getCaracterizacaoImovel = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/caracteracao-imovel', request);
            };

            var getCaracterizacaoImovelPorUf = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/caracteracao-imovel-uf', request);
            };

            var getDemandaPorMotivacao = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/demanda-por-motivacao', request);
            };

            var getDemandaPorMotivacaoPorUf = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/demanda-por-motivacao-uf', request);
            };

            var getRelatoriosValidosPorNatureza = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/relatorio-por-natureza', request);
            };

            var getRelatoriosValidosPorNaturezaPorUf = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/relatorio-por-natureza-uf', request);
            };

            var getDemandasExecutadasPorPlanoAnual = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/demanda-por-plano-anual', request);
            };

            var getDemandasExecutadasPorPlanoAnualUf = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/demanda-por-plano-anual-uf', request);
            };

            var getDemandasExecutadasPorAssunto = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/demanda-por-assunto', request);
            };

            var getDemandasExecutadasPorAssuntoUf = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/demanda-por-assunto-uf', request);
            };

            var localizacaoPorRelatorio = function (filtro) {
                var request = angular.copy(filtro);
                return $http.post(baseUrl + '/localizacao-por-relatorio', request);
            };

            var getUsuario = function () {
                return $http.get(baseUrl + '/usuario');
            };

            return {
                getCaracterizacaoImovel: getCaracterizacaoImovel,
                getDemandaPorMotivacao: getDemandaPorMotivacao,
                getRelatoriosValidosPorNatureza: getRelatoriosValidosPorNatureza,
                getDemandasExecutadasPorPlanoAnual: getDemandasExecutadasPorPlanoAnual,
                getDemandasExecutadasPorAssunto: getDemandasExecutadasPorAssunto,
                localizacaoPorRelatorio: localizacaoPorRelatorio,
                getUsuario: getUsuario,
                getCaracterizacaoImovelPorUf: getCaracterizacaoImovelPorUf,
                getDemandaPorMotivacaoPorUf:getDemandaPorMotivacaoPorUf,
                getRelatoriosValidosPorNaturezaPorUf:getRelatoriosValidosPorNaturezaPorUf,
                getDemandasExecutadasPorPlanoAnualUf:getDemandasExecutadasPorPlanoAnualUf,
                getDemandasExecutadasPorAssuntoUf:getDemandasExecutadasPorAssuntoUf
            };
        }]);
})();

(function() {
    'use strict';
    angular.module('su-fiscalizacao').controller('PainelGerencialController', ['$scope', '$filter', '$rootScope', '$state', '$stateParams', '$timeout', 'PainelGerencialService', 'ComumFactory', 'IntegradorService', 'ciMapaLayersFactoryFiscalizacao', 'ciMapaViewFactoryFiscalizacao', 'NotificadorServiceFiscalizacao', 'MAPA_CONSTANTS_FISCALIZACAO', 'CONSTANTS', '$mdDialog', 'ciMapaServiceFiscalizacao', controller]);

    function controller($scope, $filter, $rootScope, $state, $stateParams, $timeout, service, ComumFactory, IntegradorService, ciMapaLayersFactoryFiscalizacao, ciMapaViewFactoryFiscalizacao, NotificadorServiceFiscalizacao, MAPA_CONSTANTS_FISCALIZACAO, CONSTANTS, $mdDialog, ciMapaServiceFiscalizacao) {
        var scope = this;

        scope.image = {};
        scope.labels = [];
        scope.series = ['Metas Executadas', 'Metas Previstas'];
        scope.colors = ['#6696C5', '#D88850'];
        scope.data = [];
        scope._demandas = [];

        scope.permitirAcao = false;
        scope.showTabela = true;

        scope._tabela = [];
        scope.total = {};

        scope.init = function() {
            scope.getUsuario(function() {
                scope.getEstados(function() {
                    scope.getTipoGrafico(function() {
                        scope.limparFiltros();
                    });
                });
            });
        };

        scope.limparFiltros = function() {
            if ($scope.formRegistro) {
                $scope.formRegistro.$setUntouched();
                $scope.formRegistro.$setPristine();
            }
            scope.permitirAcao = false;

            var d = new Date();
            var ano = d.getFullYear();
            var dataInicial = new Date('01/01/' + ano);
            var dataFinal = new Date('12/31/' + ano);

            scope.filtros = {};
            scope.filtros.estado = [];
            scope.filtros.dtInicio = dataInicial;
            scope.filtros.dtFim = dataFinal;
            scope.filtros.graficos = [];

            scope.demandasMapaCalor = null;
            scope._caracterizacao = null;
            scope._motivacao = null;
            scope._natureza = null;
            scope._planoAnual = null;
            scope._assunto = null;

            scope.visualizarMapa = false;
        };


        scope.getCaracterizacaoImovel = function() {
            service.getCaracterizacaoImovel(scope.filtros).then(function(objectReturn) {
                var total = 0;

                scope._caracterizacao = {
                    data: [],
                    labels: [],
                    series: [],
                    colors: [
                        '#599BD5',
                        '#EC7D31',
                        '#A5A5A5',
                        '#FEBF00'
                    ],
                    options: {
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                            labels: {
                                render: "percentage",
                                precision: 0,
                                showActualPercentages: true,
                                fontSize: 12,
                                fontColor: "#000000",
                                fontStyle: "bold",
                            }
                        }
                    }
                };



                for (var i = 0; i < objectReturn.data.length; i++) {
                    var data = objectReturn.data[i];

                    total = total + data.quantidade;
                    scope._caracterizacao.data.push(data.quantidade);
                    scope._caracterizacao.labels.push(data.nome);
                    scope._caracterizacao.series.push(data.nome);
                }

                scope.totalCaracterizacao = total;
            });
        };

        scope.getDemandaPorMotivacao = function() {
            service.getDemandaPorMotivacao(scope.filtros).then(function(objectReturn) {
                scope._motivacao = {
                    data: [],
                    labels: [],
                    series: [],
                    colors: [
                        '#599BD5',
                        '#EC7D31',
                        '#A5A5A5',
                        '#FEBF00',
                        '#4872BD',
                        '#72AB4A',
                        '#2A5F87',
                        '#974914',
                        '#626262',
                        '#967207'
                    ],
                    options: {
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                            labels: {
                                render: "percentage",
                                precision: 0,
                                showActualPercentages: true,
                                fontSize: 12,
                                fontColor: "#000000",
                                fontStyle: "bold",
                            }
                        }
                    }
                };

                var total = 0;

                for (var i = 0; i < objectReturn.data.length; i++) {
                    var data = objectReturn.data[i];

                    total = total + data.quantidade;

                    scope._motivacao.data.push(data.quantidade);
                    scope._motivacao.labels.push(data.nome);
                    scope._motivacao.series.push(data.nome);
                }

                scope.totalMotivacao = total;
            });
        };

        scope.getRelatoriosValidosPorNatureza = function() {
            service.getRelatoriosValidosPorNatureza(scope.filtros).then(function(objectReturn) {
                scope._natureza = {
                    data: [],
                    labels: [],
                    series: [],
                    colors: [
                        '#3A64AE',
                        '#90A4D4'
                    ],
                    options: {
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                            labels: {
                                render: "percentage",
                                precision: 0,
                                showActualPercentages: true,
                                fontSize: 12,
                                fontColor: "#000000",
                                fontStyle: "bold",
                            }
                        }
                    }
                };

                var total = 0;
                for (var i = 0; i < objectReturn.data.length; i++) {
                    var data = objectReturn.data[i];

                    total = total + data.quantidade;
                    scope._natureza.data.push(data.quantidade);
                    scope._natureza.labels.push(data.nome);
                    scope._natureza.series.push(data.nome);
                }

                scope.totalNatureza = total;
            });
        };

        scope.getDemandasExecutadasPorPlanoAnual = function() {
            service.getDemandasExecutadasPorPlanoAnual(scope.filtros).then(function(objectReturn) {
                scope._planoAnual = {
                    data: [],
                    labels: [],
                    series: [],
                    colors: [
                        '#599BD5',
                        '#EC7D31'
                    ],
                    options: {
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                            labels: {
                                render: "percentage",
                                precision: 0,
                                showActualPercentages: true,
                                fontSize: 12,
                                fontColor: "#000000",
                                fontStyle: "bold",
                            }
                        }
                    }
                };

                var total = 0;
                for (var i = 0; i < objectReturn.data.length; i++) {
                    var data = objectReturn.data[i];

                    total = total + data.quantidade;
                    scope._planoAnual.data.push(data.quantidade);
                    scope._planoAnual.labels.push(data.nome);
                    scope._planoAnual.series.push(data.nome);
                }


                scope.totalPlanoAnual = total;
            });
        };

        scope.getDemandasExecutadasPorAssunto = function() {
            service.getDemandasExecutadasPorAssunto(scope.filtros).then(function(objectReturn) {
                scope._assunto = {
                    data: [],
                    labels: [],
                    series: [],
                    colors: [
                        '#599BD5',
                        '#EC7D31',
                        '#A5A5A5',
                        '#FEBF00',
                        '#4872BD',
                        '#72AB4A',
                        '#2A5F87',
                        '#974914',
                        '#626262',
                        '#967207',
                        '#2500ec',
                        '#a5a43a',
                        '#fe3900',
                        '#54bd00',
                        '#ab2e87',
                        '#4a2187',
                        '#946a97',
                        '#00a1a8',
                        '#000000'
                    ],
                    options: {
                        responsive: true,
                        maintainAspectRatio: true,
                        plugins: {
                            labels: {
                                render: "value",
                                fontSize: 12,
                                fontColor: "#000000",
                                fontStyle: "bold",
                                showZero: true,
                            }
                        }
                    }
                };

                var total = 0;
                for (var i = 0; i < objectReturn.data.length; i++) {
                    var data = objectReturn.data[i];

                    total = total + data.quantidade;
                    scope._assunto.data.push(data.quantidade);
                    scope._assunto.labels.push(data.nome);
                    scope._assunto.series.push(data.nome);
                }

                scope.totalAssunto = total;
            });
        };

        scope.filtrar = function() {
            $scope.formRegistro.$setSubmitted();
            scope.limparDadosDaTela();

            setTimeout(function() {
                if ($scope.formRegistro.$valid) {

                    scope.filtros.estado.sort(function(a, b) {
                        return a < b ? -1 : a > b ? 1 : 0;
                    });

                    scope.ufs = scope.filtros.estado.toString();

                    if (scope.filtros.graficos.indexOf(CONSTANTS.TIPO_GRAFICO.CARACTERIZACAO_IMOVEIS_FISCALIZADOS) > -1) {
                        scope.getCaracterizacaoImovel();
                    }

                    if (scope.filtros.graficos.indexOf(CONSTANTS.TIPO_GRAFICO.DEMANDAS) > -1) {
                        scope.getDemandaPorMotivacao();
                    }

                    if (scope.filtros.graficos.indexOf(CONSTANTS.TIPO_GRAFICO.NATUREZA_IMOVEL) > -1) {
                        scope.getRelatoriosValidosPorNatureza();
                    }

                    if (scope.filtros.graficos.indexOf(CONSTANTS.TIPO_GRAFICO.PLANO_ANUAL_FISCALIZACAO) > -1) {
                        scope.getDemandasExecutadasPorPlanoAnual();
                    }

                    if (scope.filtros.graficos.indexOf(CONSTANTS.TIPO_GRAFICO.DEMANDAS_MAIS_EXECUTADAS) > -1) {
                        scope.getDemandasExecutadasPorAssunto();
                    }

                    if (scope.filtros.graficos.indexOf(CONSTANTS.TIPO_GRAFICO.FISCALIZACOES_BRASIL) > -1) {
                        scope.visualizarMapa = true;
                        scope.iniciarMapa(function() {
                            scope.localizacaoPorRelatorio(function() {});
                        });
                    }

                } else {
                    ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
                }
            }, 500);
        };

        scope.limparDadosDaTela = function() {
            scope._caracterizacao = false;
            scope._motivacao = false;
            scope._natureza = false;
            scope._planoAnual = false;
            scope._assunto = false;
            scope.visualizarMapa = false;
        }

        scope.getUsuario = function(callback) {
            scope._usuario = {};
            service.getUsuario().then(function(objectReturn) {
                scope._usuario = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.getEstados = function(callback) {
            scope._estados = [];
            IntegradorService.getEstados().then(function(objectReturn) {
                objectReturn.data.forEach(function(estado) {
                    scope._usuario.jurisdicoes.forEach(function(juridicao) {
                        if (juridicao === estado.sigla) {
                            scope._estados.push(estado);
                        }
                    });
                });

                if (callback) {
                    callback();
                }
            });
        };

        scope.getTipoGrafico = function(callback) {
            scope._tipoGraficos = [];

            scope._tipoGraficos = CONSTANTS.TIPO_GRAFICO;

            if (callback) {
                callback();
            }
        };

        scope.selecionaTodosEstados = function() {
            scope.filtros.estado = [];

            scope._estados.forEach(function(estado) {
                scope.filtros.estado.push(estado.sigla);
            });
        };

        scope.pdfJsonGenerator = function() {
            var today = new Date();
            var year = today.getFullYear();
            var month = today.getMonth() + 1;
            if (month < 10) {
                month = '0' + month;
            }
            var day = today.getDate();
            if (day < 10) {
                day = '0' + day;
            }
            var fullYear = day + '/' + month + '/' + year;

            var pdf = {
                content: [{
                        style: 'table',

                        table: {
                            widths: ['*'],
                            body: [
                                [
                                    { text: fullYear, bold: false, fontSize: 10, alignment: 'right' }
                                ]
                            ]
                        },

                        layout: 'noBorders'
                    },
                    {
                        style: 'table',

                        table: {
                            widths: [550],
                            body: [
                                [

                                ]
                            ]
                        },

                        layout: 'noBorders'
                    },
                    {
                        style: 'table',

                        table: {
                            widths: [550],
                            body: [
                                [

                                ]
                            ]
                        },

                        layout: 'noBorders'
                    },
                    {
                        style: 'table',

                        table: {
                            widths: [550],
                            body: [
                                [
                                    { image: scope.pngChartAssunto, width: 550, margin: [0, 0, 0, 10] }
                                ]
                            ]
                        },

                        layout: 'noBorders'
                    }
                ],
                pageSize: 'A4',
                pageOrientation: 'portrait',
                // [left, top, right, bottom] or [horizontal, vertical] or just a number for equal margins
                pageMargins: [20, 20]
            };

            var ChartCaracterizacaoMotivacao = scope.addChartCaracterizacaoMotivacao();
            var ChartNaturezaAssunto = scope.addChartNaturezaAssunto();
            var chartDemandaMapaCalor = scope.addChartDemandaMapaCalor();

            pdf.content[1].table.body = ChartCaracterizacaoMotivacao;
            pdf.content[2].table.body = ChartNaturezaAssunto;
            pdf.content[3].table.body = chartDemandaMapaCalor;

            return pdf;
        };

        scope.addChartCaracterizacaoMotivacao = function() {
            var body = [];

            body.push([
                { image: scope.pngChartCaracterizacao, width: 550, margin: [0, 10, 0, 30] }
            ]);

            body.push([
                { image: scope.pngChartMotivacao, width: 550, margin: [0, 10, 0, 10] }
            ]);

            return body;
        };

        scope.addChartNaturezaAssunto = function() {
            var body = [];

            body.push([
                { image: scope.pngChartNatureza, width: 550, margin: [0, 10, 0, 10] }
            ]);

            body.push([
                { image: scope.pngChartAnual, width: 550, margin: [0, 10, 0, 10] }
            ]);

            return body;
        };

        scope.addChartDemandaMapaCalor = function() {
            var body = [];

            body.push([
                { image: scope.pngChartAssunto, width: 550, margin: [0, 0, 0, 10] }
            ]);

            body.push([
                { image: scope.pngChartMapacalor, width: 550, margin: [0, 0, 0, 10] }
            ]);

            return body;
        };

        scope.printPdf = function() {
            scope.options = {};
            scope.options.hight = 1000;
            scope.options.width = 1000;

            domtoimage.toPng(document.getElementById('caracterizacao'), scope.options).then(function(dataUrl) {
                scope.pngChartCaracterizacao = angular.copy(dataUrl);
                domtoimage.toPng(document.getElementById('motivacao'), scope.options).then(function(dataUrl) {
                    scope.pngChartMotivacao = angular.copy(dataUrl);
                    domtoimage.toPng(document.getElementById('natureza'), scope.options).then(function(dataUrl) {
                        scope.pngChartNatureza = angular.copy(dataUrl);
                        domtoimage.toPng(document.getElementById('Anual'), scope.options).then(function(dataUrl) {
                            scope.pngChartAnual = angular.copy(dataUrl);
                            domtoimage.toPng(document.getElementById('assunto'), scope.options).then(function(dataUrl) {
                                scope.pngChartAssunto = angular.copy(dataUrl);
                                domtoimage.toPng(document.getElementById('mapa-calor'), scope.options).then(function(dataUrl) {
                                    scope.pngChartMapacalor = angular.copy(dataUrl);
                                    pdfMake.createPdf(scope.pdfJsonGenerator()).print({});
                                    $scope.$apply();
                                });
                            });
                        });
                    });
                });
            });
        };

        scope.iniciarMapa = function(callback) {
            ciMapaLayersFactoryFiscalizacao.init();
            ciMapaViewFactoryFiscalizacao.init();

            if (callback) {
                callback();
            }
        };

        scope.localizacaoPorRelatorio = function(callback) {
            service.localizacaoPorRelatorio(scope.filtros).then(function(objectReturn) {
                scope.demandasMapaCalor = angular.copy(objectReturn.data);

                var listaCoordenadas = [];
                scope.totalMapa = 0;
                angular.forEach(scope.demandasMapaCalor, function(mapaCalor, key) {
                    var mapa = mapaCalor.mapaCalorDTO;
                    scope.totalMapa = scope.totalMapa + mapa.length;
                    for (var i = 0; i < mapa.length; i++) {
                        if (mapa[i].latitude && mapa[i].longitude) {
                            listaCoordenadas.push({
                                coordenadas: {
                                    'coordinates': [mapa[i].latitude, mapa[i].longitude],
                                    'type': 'Point'
                                }
                            });
                        } else {
                            scope.croqui = JSON.parse(mapa[i].croqui);

                            if (scope.croqui && scope.croqui.coordinates) {
                                for (var j = 0; j < scope.croqui.coordinates.length; j++) {
                                    listaCoordenadas.push({
                                        coordenadas: {
                                            'coordinates': [scope.croqui.coordinates[j]],
                                            'type': 'Polygon'
                                        }
                                    });
                                }
                            }
                        }
                    }
                });



                if (listaCoordenadas && listaCoordenadas.length > 0) {
                    $timeout(function() {
                        NotificadorServiceFiscalizacao.notify(scope, listaCoordenadas, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.MARCAR_LISTA_COORDENADAS);
                    }, 1000);
                }

                if (callback) {
                    callback();
                }
            });
        };

        scope.showAlert = function(ev) {
            $mdDialog.show({
                controller: function() {
                    return scope;
                },
                controllerAs: 'scope',
                templateUrl: 'scripts-fiscalizacao/pages/painel/modalTemplate/modalTemplate.tmpl.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose: true,
                fullscreen: true
            });
        };


        scope.pngGenerator = function() {
            var div = document.getElementById('dv-frame-mapa');

            var yearInicial = scope.filtros.dtInicio.getFullYear();
            var monthInicial = scope.filtros.dtInicio.getMonth() + 1;
            monthInicial = monthInicial < 10 ? '0' + monthInicial : monthInicial;
            var dayInicial = scope.filtros.dtInicio.getDate();
            dayInicial = dayInicial < 10 ? '0' + dayInicial : dayInicial;

            var dataInicial = dayInicial + '/' + monthInicial + '/' + yearInicial;

            var yearFinal = scope.filtros.dtFim.getFullYear();
            var monthFinal = scope.filtros.dtFim.getMonth() + 1;
            monthFinal = monthFinal < 10 ? '0' + monthFinal : monthFinal;
            var dayFinal = scope.filtros.dtFim.getDate();
            dayFinal = dayFinal < 10 ? '0' + dayFinal : dayFinal;

            var dataFinal = dayFinal + '/' + monthFinal + '/' + yearFinal;

            div.innerHTML = '<div style="margin: 10px 0"><strong><h3>Fiscalizações no Brasil</h3></strong></div>';
            div.innerHTML += '<div style="margin: 10px 0" ><strong>UF(s): </strong>' + scope.ufs.toString() + '</div>';
            div.innerHTML += '<div style="margin: 10px 0" ><strong>Período: </strong>' + dataInicial + ' a ' + dataFinal + '</div>';
            div.innerHTML += '<div style="margin: 10px 0" ><strong>Total: </strong>' + scope.totalMapa.toString() + '</div>';
        }

        scope.gerarPng = function() {
            scope.isGerar = true;
            var elemento = document.getElementById('pgn-frame-mapa');
            scope.pngGenerator();

            setTimeout(function() {
                domtoimage.toPng(elemento).then(function(dataUrl) {
                    var link = document.createElement('a');
                    scope.pngChart = angular.copy(dataUrl);
                    link.download = 'FISCALIZAÇÕES NO BRASIL';
                    link.href = dataUrl;
                    link.click();
                    document.getElementById('dv-frame-mapa').innerHTML = '';
                    scope.isGerar = false;
                });
            }, 2000);

        };

        scope.pdfJsonGeneratorMapaCalor = function() {
            var today = new Date();
            var year = today.getFullYear();
            var month = today.getMonth() + 1;
            if (month < 10) {
                month = '0' + month;
            }
            var day = today.getDate();
            if (day < 10) {
                day = '0' + day;
            }
            var fullYear = day + '/' + month + '/' + year;

            return {
                content: [{
                        style: 'table',

                        table: {
                            widths: ['*'],
                            body: [
                                [
                                    { text: fullYear, bold: false, fontSize: 10, alignment: 'right' }
                                ]
                            ]
                        },

                        layout: 'noBorders'
                    },
                    {
                        style: 'table',

                        table: {
                            widths: ['*'],
                            body: [
                                [
                                    { text: 'Fiscalizações no Brasil', bold: true, fontSize: 10, alignment: 'center', margin: [0, 10, 0, 10] }
                                ]
                            ]
                        },

                        layout: 'noBorders'
                    },
                    {
                        style: 'table',

                        table: {
                            widths: [550],
                            body: [
                                [
                                    { image: scope.pngChart, width: 550, margin: [0, 0, 0, 10] }
                                ]
                            ]
                        },

                        layout: 'noBorders'
                    }
                ],
                pageSize: 'A4',
                pageOrientation: 'portrait',
                // [left, top, right, bottom] or [horizontal, vertical] or just a number for equal margins
                pageMargins: [20, 20]
            };
        };

        scope.downloadPDF = function() {
            domtoimage.toPng(document.getElementById('pdf-mapa-calor')).then(function(dataUrl) {
                scope.pngChart = angular.copy(dataUrl);
                pdfMake.createPdf(scope.pdfJsonGeneratorMapaCalor()).download('FISCALIZAÇÕES NO BRASIL' + '.pdf', function() {
                    $scope.$apply();
                });
            });
        };

        scope.enquadrarUf = function(uf) {
            if (uf) {
                ciMapaServiceFiscalizacao.buscarLocalidadesPorCoordenadas(uf)
                    .then(function(response) {
                        if (response.status === 200 && response.data.length > 0) {
                            var firstMatch = response.data[0];
                            var matchCoordinates = firstMatch.geojson.coordinates;
                            var geometry = new ol.geom.Polygon(matchCoordinates);
                            $rootScope.$broadcast('enquadrarUf', geometry);
                        }
                    }).finally(function() {

                    });
            }
        }

        scope.init();
    }
})();
(function () {
    'use strict';
    angular.module('su-fiscalizacao').factory('MetaService', ["$http", function ($http) {

        var baseUrl = '/fiscalizacao/api/meta';

        var create = function (registro) {
            return $http.post(baseUrl, registro);
        };

        var update = function (registro) {
            return $http.put(baseUrl, registro);
        };

        var findByAno = function (ano) {
            return $http.get(baseUrl + '/' + ano);
        };

        return {
            create: create,
            update: update,
            findByAno: findByAno
        };
    }]);
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('MetaController', ['$scope', '$rootScope', '$state', '$stateParams', '$timeout', 'MetaService', 'ComumFactory', 'IntegradorService', 'DemandaService', controller]);

    function controller($scope, $rootScope, $state, $stateParams, $timeout, service, ComumFactory, IntegradorService, DemandaService) {
        var scope = this;

        scope.apenasLeitura = false;
        scope.anoSelecionado = false;
        scope.permitirAcao = false;

        scope.init = function () {
            scope.getUsuario(function () {
                scope.limpar();

                scope.estrutura = {};
                scope.getMeses();


                scope.getEstados(function () {
                    scope.estrutura.estados = angular.copy(scope._estados);
                });
            });
        };

        scope.limpar = function () {
            scope.anoSelecionado = false;
            scope.permitirAcao = false;
            scope.registro = {};
            scope.registro.ano = null;
            scope.registro.estados = [];
        };

        scope.findBy = function (ano) {
            if (ano && ano.toString().length == 4) {
                scope.anoSelecionado = true;
                scope.validaData();
                service.findByAno(ano).then(function (objectResponse) {
                    if (objectResponse.data && objectResponse.data.id) {
                        scope.registro = objectResponse.data;
                    } else {
                        var ano = angular.copy(scope.registro.ano);
                        scope.registro = angular.copy(scope.estrutura);
                        scope.registro.ano = ano;
                    }
                    scope.permitirAcao = true;
                }, function (error) {
                    ComumFactory.mostrarMensagemError(error.data.message);
                });
            } else {
                ComumFactory.mostrarMensagemError('Insira um ano válido!');
            }
        };

        scope.persistir = function (registro) {
            if (registro.id) {
                service.update(registro).then(function () {
                    scope.limpar();
                });
            } else {
                service.create(registro).then(function () {
                    scope.limpar();
                });
            }
        };

        scope.validaData = function () {
            var ano = scope.registro.ano + 1;
            var date = new Date('01/31/' + ano);

            scope.apenasLeitura = date < new Date();
            if (scope.apenasLeitura) {
                ComumFactory.mostrarMensagemError('O sistema somente permite a alteração das Metas até dia 31/01 do ano subsequente ao ano atual!');
            }
        };

        scope.getUsuario = function (callback) {
            scope._usuario = {};
            DemandaService.getUsuario().then(function (objectReturn) {
                scope._usuario = objectReturn.data;

                scope._jurisdicao = getJurisdicao();

                if (callback) {
                    callback();
                }
            });
        };

        function getJurisdicao() {
            if (!scope._usuario.jurisdicoes || scope._usuario.jurisdicoes.length > 1) {
                return 'DF';
            } else {
                return scope._usuario.jurisdicoes[0];
            }
        }

        scope.getMeses = function () {
            scope._meses = [];
            scope._meses.push({mes: 1});
            scope._meses.push({mes: 2});
            scope._meses.push({mes: 3});
            scope._meses.push({mes: 4});
            scope._meses.push({mes: 5});
            scope._meses.push({mes: 6});
            scope._meses.push({mes: 7});
            scope._meses.push({mes: 8});
            scope._meses.push({mes: 9});
            scope._meses.push({mes: 10});
            scope._meses.push({mes: 11});
            scope._meses.push({mes: 12});
        };

        scope.getEstados = function (callback) {
            scope._estados = [];
            IntegradorService.getEstados().then(function (objectReturn) {
                var estados = objectReturn.data;

                for (var i = 0; i < estados.length; i++) {
                    var estado = estados[i];

                    scope._estados.push({
                        sigla: estado.sigla,
                        meses: angular.copy(scope._meses)
                    });
                }

                if (callback) {
                    callback();
                }
            });
        };

        scope.showAlert = function (ev) {
            $mdDialog.show({
                controller: function () {
                    return scope;
                },
                controllerAs: 'scope',
                templateUrl: 'scripts-fiscalizacao/pages/painel/modalTemplate/modalTemplate.tmpl.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                clickOutsideToClose: true,
                fullscreen: true
            });
        };

        scope.cancelModal = function (callback) {
            $mdDialog.cancel();

            if (callback) {
                callback();
            }
        };

        scope.pdfJsonGenerator = function () {
            var today = new Date();
            var year = today.getFullYear();
            var month = today.getMonth() + 1;
            if (month < 10) {
                month = '0' + month;
            }
            var day = today.getDate();
            if (day < 10) {
                day = '0' + day;
            }
            var fullYear = day + '/' + month + '/' + year;

            var pdf = {
                content: [
                    {
                        style: 'table',

                        table: {
                            widths: ['*'],
                            body: [
                                [
                                    {text: fullYear, alignment: 'right', fontSize: 10}
                                ]
                            ]
                        },

                        layout: 'noBorders'
                    },
                    {
                        style: 'table',
                        table: {
                            widths: ['*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*', '*'],
                            body: []
                        },
                        layout: {
                            hLineWidth: function (i, node) {
                                return (i === 0 || i === node.table.body.length) ? 1 : 1;
                            },
                            vLineWidth: function (i, node) {
                                return (i === 0 || i === node.table.widths.length) ? 1 : 1;
                            },
                            hLineColor: function (i, node) {
                                return (i === 0 || i === node.table.body.length) ? '' : 'black';
                            },
                            vLineColor: function (i, node) {
                                return (i === 0 || i === node.table.widths.length) ? '' : 'black';
                            }
                        }
                    }
                ],
                styles: {
                    titleMargin: {
                        margin: [5, 17, 18, 0]
                    },
                    imageMargin: {
                        margin: [0, 15, 5, 0]
                    },
                    cellColor: {
                        fillColor: '#4CAF50'
                    },

                    table: {
                        margin: [10, 11, 20, 1]
                    }
                },
                pageSize: 'A4',
                pageOrientation: 'landscape',
                // [left, top, right, bottom] or [horizontal, vertical] or just a number for equal margins
                pageMargins: [20, 20]
            };

            var dados = scope.addDados();
            pdf.content[1].table.body = dados;

            return pdf;
        };

        scope.addDados = function () {
            var body = [];
            body.push([
                {text: 'Metas', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor', colSpan: 13},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: '', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'}
            ]);

            body.push([
                {text: 'Regiões', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Janeiro', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Fevereiro', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Março', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Abril', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Maio', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Junho', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Julho', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Agosto', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Setembro', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Outubro', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Novembro', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'},
                {text: 'Dezembro', bold: true, fontSize: 10, alignment: 'center', color: 'white', style: 'cellColor'}
            ]);

            var regioes = [];
            for (var i = 0; i < scope.registro.estados.length; i++) {
                regioes.push(angular.copy(scope.registro.estados[i]));
                for (var j = 0; j < regioes[i].meses.length; j++) {
                    if (regioes[i].meses[j].meta == '' || regioes[i].meses[j].meta == null) {
                        regioes[i].meses[j].meta = '';
                    }
                }

                var column = [];
                column.push(
                    {text: regioes[i].sigla, fontSize: 10},
                    {text: regioes[i].meses[0].meta, fontSize: 10},
                    {text: regioes[i].meses[1].meta, fontSize: 10},
                    {text: regioes[i].meses[2].meta, fontSize: 10},
                    {text: regioes[i].meses[3].meta, fontSize: 10},
                    {text: regioes[i].meses[4].meta, fontSize: 10},
                    {text: regioes[i].meses[5].meta, fontSize: 10},
                    {text: regioes[i].meses[6].meta, fontSize: 10},
                    {text: regioes[i].meses[7].meta, fontSize: 10},
                    {text: regioes[i].meses[8].meta, fontSize: 10},
                    {text: regioes[i].meses[9].meta, fontSize: 10},
                    {text: regioes[i].meses[10].meta, fontSize: 10},
                    {text: regioes[i].meses[11].meta, fontSize: 10}
                );
                body.push(column);
            }

            return body;
        };

        scope.downloadPDF = function () {
            pdfMake.createPdf(scope.pdfJsonGenerator()).download('METAS.pdf', function () {
                $scope.$apply();
            });
        };

        scope.printPdf = function () {
            pdfMake.createPdf(scope.pdfJsonGenerator()).print({});
            $scope.$apply();
        };

        scope.gerarCsv = function (registro) {
            var meta;
            if (registro.id) {
                service.update(registro).then(function (resp) {
                    meta = resp.data;
                    gerar();
                });
            } else {
                service.create(registro).then(function (resp) {
                    meta = resp.data;
                    gerar();
                });
            }

            function gerar() {
                var a = document.createElement('a');
                a.setAttribute('href', '/fiscalizacao/api/meta/csv/' + meta.id);
                a.setAttribute('target', '_blank');
                var clickEvent = new MouseEvent('click', {
                    'view': window,
                    'bubbles': true,
                    'cancelable': false
                });
                a.dispatchEvent(clickEvent);
            }
        };

        scope.gerarExcel = function (registro) {
            var meta;
            if (registro.id) {
                service.update(registro).then(function (resp) {
                    meta = resp.data;
                    gerar();
                });
            } else {
                service.create(registro).then(function (resp) {
                    meta = resp.data;
                    gerar();
                });
            }

            function gerar() {
                var a = document.createElement('a');
                a.setAttribute('href', '/fiscalizacao/api/meta/excel/' + meta.id);
                a.setAttribute('target', '_blank');
                var clickEvent = new MouseEvent('click', {
                    'view': window,
                    'bubbles': true,
                    'cancelable': false
                });
                a.dispatchEvent(clickEvent);
            }
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('OrdemFiscalizacaoListarController', ['$scope', '$mdDialog', 'OrdemFiscalizacaoService', controller]);

    function controller($scope, $mdDialog, service) {
        var scope = this;
        scope.demanda = {};
        scope.exibirTodos = false;
        scope.agenteExterno = false;
        scope.tabelaAgenteExterno = false;

        scope.tamanhoLimite = 10;
        scope.registroInicial = 1;
        scope.optionTamanho = [10, 20, 50];
        scope.maxRegistros = 10;
        scope.totalItems = 0;

        scope.filtros = {
            order: 'protocolo',
            orderBy: 'ASC',
            inicio: '',
            fim: '',
            dataEdicao: '',
            viagem: false
        };

        scope.init = function () {
            scope.getStatus();
            scope.getUsuario(function () {
                scope.limparFiltros();
            });
        };

        scope.limparFiltros = function () {
            scope.filtros = {
                order: 'protocolo',
                orderBy: 'ASC',
                inicio: '',
                fim: '',
                viagem: false
            };
            scope.filtros.inicio = '';
            scope.filtros.fim = '';
            scope.filtrar();
        };

        scope.filtrar = function () {
            if(!$scope.formRegistro.$valid){
                return;
            }

            scope.filtros.page = scope.registroInicial - 1;
            scope.filtros.size = scope.tamanhoLimite;

            if (scope.filtros.orderBy === 'ASC') {
                scope.filtros.orderBy = 'DESC';
                scope.filtros.order = scope.filtros.order.replace('-', '');
            } else {
                scope.filtros.orderBy = 'ASC';
                scope.filtros.order = scope.filtros.order.replace('-', '');
            }

            if(scope.agenteExterno){
                scope.filtros.order = 'status';
            service.findByFilterAgenteExterno(scope.filtros).then(function (retorno) {
                scope._registros = retorno.data.content || [];
                console.log(scope._registros);
                scope.totalItems = retorno.data.totalElements;
                scope.tabelaAgenteExterno = true;
            });
        }else{
            service.findByFilter(scope.filtros).then(function (retorno) {
                scope._registros = retorno.data.content || [];
                scope.totalItems = retorno.data.totalElements;
                scope.tabelaAgenteExterno = false;
            });

        }

            if (scope.filtros.orderBy === 'DESC') {
                scope.filtros.order = '-' + scope.filtros.order;
            }
        };

        scope.getAgenteExterno = function () {

            scope._registros = [];
            scope.totalItems = null;
            scope.getStatus();

            scope.filtrar();
            
                };

        scope.getStatus = function (callback) {
            scope._status = {};
            if(scope.agenteExterno){

                service.getStatusAgenteExterno().then(function (objectReturn) {
                    scope._status = objectReturn.data;
    
                    if(callback){
                        callback();
                    }
                });

            }else{
            service.getStatus().then(function (objectReturn) {
                scope._status = objectReturn.data;

                if(callback){
                    callback();
                }
            });
        }
        };

        scope.getUsuario = function (callback) {
            scope._usuario = {};
            service.getUsuario().then(function (objectReturn) {
                scope._usuario = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.excluirRegistro = function (registro) {
            var confirm = $mdDialog.confirm()
                .title('Atenção!')
                .textContent('Tem certeza que deseja cancelar esta Ordem de Fiscalização?')
                .ok('Confirmar')
                .cancel('Cancelar');

            $mdDialog.show(confirm).then(function () {
                
                service.deleteOrdemFiscalizacao(registro).then(function () {
                    scope.filtrar();
                });
                
            });
        };

        scope.cancelarRegistro = function (registro) {
            var confirm = $mdDialog.confirm()
                .title('Atenção!')
                .textContent('Tem certeza que deseja cancelar esta Fiscalização Executada por Agente Externo?')
                .ok('Confirmar')
                .cancel('Cancelar');

            $mdDialog.show(confirm).then(function () {
                service.cancelarFiscalizacaoAgenteExterno(registro).then(function () {
                    scope.filtrar();
                });
            });
        };

        scope.getDemanda = function(demandaId, ofId){
            service.getDemanda(demandaId).then(function(response){
                scope.demanda = response.data;
                scope.demanda.ofId = ofId;
            });
        };

        scope.handleExibirDemanda = function(of){
            !of.exibirTodos ? of.exibirTodos = true : of.exibirTodos = false;
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').factory('AgenteExternoService', ["$http", "Upload", function ($http, Upload) {

        var baseUrl = '/fiscalizacao/api/fiscalizacao-agente-externo';
        var uploadUrl = '/fiscalizacao/api/arquivo';

        var create = function (registro) {
            return $http.post(baseUrl, registro);
        };

        var autuar = function (registro) {
            return $http.post(baseUrl + "/autuar", registro);
        };

        var update = function (registro) {
            return $http.put(baseUrl, registro);
        };

        var getHistorico = function (registroId) {
            return $http.get(baseUrl + "/historico/" + registroId);
        };

        var findById = function (registroId) {
            return $http.get(baseUrl + "/" + registroId);
        };

        var getUsuario = function () {
            return $http.get(baseUrl + "/usuario");
        };

        var buscarAllDemandasByUF = function (filtros) {
            return $http.post(baseUrl + "/demanda/all?page=" + filtros.page + "&size=" + filtros.size + "&sort="+ filtros.order+ "," + filtros.orderBy);
        };

        var getListaTipoAdministracao = function () {
            return $http.get(baseUrl + "/tipoAdministracao");
        };
        
        var buscarDemandas = function (filtros,registro) {
            return $http.post(baseUrl + "/demanda?page=" + filtros.page + "&size=" + filtros.size + "&sort="+ filtros.order+ "," + filtros.orderBy, registro);
        };

        var getStatus = function () {
            return $http.get(baseUrl + "/status");
        };

        var upload = function($file){
            return Upload.upload({
                url: uploadUrl + '/upload',
                data: {
                    file: $file
                }
            });
        };


        return {
            create: create,
            autuar: autuar,
            update: update,
            findById: findById,
            buscarAllDemandasByUF: buscarAllDemandasByUF,
            getUsuario : getUsuario,
            getListaTipoAdministracao: getListaTipoAdministracao,
            getStatus: getStatus,
            upload: upload,
            getHistorico : getHistorico,
            buscarDemandas: buscarDemandas
        }
        
    }])
}
)();
(function () {
    'use strict';
    angular.module('su-fiscalizacao').
    controller('AgenteExternoDetalharController', [
        '$scope',  '$rootScope', '$filter', '$state', '$stateParams','$mdDialog', 'IntegradorService', 'OrdemFiscalizacaoService', 'AgenteExternoService',  'ComumFactory', 'MapaServiceFiscalizacao', '$timeout', controller]);

    function controller($scope, $rootScope, $filter, $state, $stateParams, $mdDialog, integradorService, OrdemFiscalizacaoService, service, ComumFactory, MapaServiceFiscalizacao, $timeout) {
        var scope = this;
        window.mdSelectOnKeyDownOverride = function(event) { event.stopPropagation(); };


        scope.init = function () {
            scope.apenasLeitura = false;
            scope.dadosDemanda = false;
            scope.exibirAutuar = false;
            scope.exibirSalvar  = false;

             //Inicia as listas de origem e destinos de estados e municípios
             scope._estadosOrigem = [];
             scope._municipiosOrigem = [];
             scope._estadosDestino = [];
             scope._municipiosDestino = [];

            scope.headerPagina(function(){
                scope.getUsuario(function () {
                    scope.carregarListas(function(){
                        if ($stateParams.id) {
                            if($state.current.name.indexOf('consultarAgenteExterno') !== -1){
                                scope.findBy($stateParams.id, function(){
                                    scope.apenasLeitura = true;
                                    scope.exibirSalvar = false;
                                })
                            }else {
                                scope.findBy($stateParams.id, function(){
                                    scope.apenasLeitura = false;
                                    scope.exibirSalvar = true;
                                });
                            }
                        }else {
                            scope.novoRegistro(function(){
                                scope.apenasLeitura = false;
                            });
                        }
                    });
                });
            });
        };

        scope.headerPagina = function (callback) {
            if ($state.current.name.indexOf('listar') === -1) {
                if ($state.current.name.indexOf('incluirAgenteExterno') !== -1) {
                    scope.tituloPagina = 'Cadastrar Fiscalização Executada por Agente Externo';
                    scope.showDemandas = false;
                    scope.show = true;
                }else if ($state.current.name.indexOf('alterarAgenteExterno') !== -1) {
                    scope.tituloPagina = 'Alterar Fiscalização Executada por Agente Externo';
                    scope.showDemandas = false;
                    scope.show = true;
                }else if ($state.current.name.indexOf('consultarAgenteExterno') !== -1) {
                    scope.tituloPagina = 'Visualizar Fiscalização Executada por Agente Externo';
                    scope.show = false;
                }
            }

            if(callback){
                callback();
            }
        };

        scope.getHistorico = function () {
            scope.buscandoHistorico = true;
            scope.mensagemHistorico = "Buscando Históricos!";
            scope._historicos = [];
            service.getHistorico($stateParams.id).then(function (objectReturn) {
                scope._historicos = objectReturn.data;

                if(scope._historicos.length ===  0){
                    scope.mensagemHistorico = "Nenhum resultado encontrado!";
                }

                scope.buscandoHistorico = false;
            });
        };

        scope.findBy = function (registroId, callback) {

            service.findById(registroId).then(function (objectResponse) {
                scope._registro = objectResponse.data;
                scope._registro.demandas = [];

                scope._registroInicial = 1;
                scope.page = 1;
                scope._tamanhoLimite = 10;
                scope._optionTamanho = [10, 20];
                scope._maxRegistros = 10;
                scope._totalItems = 0;
                scope._filtros = {order: 'dtPrazo', orderBy: 'DESC', inicio: '', fim: ''};
                scope._filtros = {order: 'dtPrazo', orderBy: 'DESC', inicio: '', fim: ''};
                scope.demanda = '';


                scope.dadosDemanda = true;

                //Popula a comboBox de tipoAdministracao com o valor do registro
                if( scope._registro.tipoAdministracao && scope._registro.tipoAdministracao.id){
                    for (var i = 0; i < scope._tipoAdministracao.length; i++) {
                        if(scope._tipoAdministracao[i].id === scope._registro.tipoAdministracao.id){
                            scope._registro.tipoAdministracao = scope._tipoAdministracao[i];
                        }
                    }
                }


                angular.forEach( scope._registro.relatorios, function(value, key){

                    if(value.dataInicio){
                        value.dataInicio = new Date(value.dataInicio);
                    }else{
                        value.dataInicio = null;
                    }

                    if(value.dataFim){
                        value.dataFim = new Date(value.dataFim);
                    }else{
                        value.dataFim = null;
                    }

                    if(value.demandaExecutada === false){
                        value.fiscalizacaoSpu = null;  
                    }
                                        

                    if($state.current.name.indexOf('alterarAgenteExterno') !== -1 ){
 
                        scope.showAtuarButton()
                    }


                    scope._registro.demandas.push(value.demanda);
                });

                if(callback){
                    callback();
                }
            });
        };

        scope.getUsuario = function (callback) {
            scope._usuario = {};
            service.getUsuario().then(function (objectReturn) {
                scope._usuario = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.carregarListas = function(callback){
            scope.getStatus(function(){
                scope.getTipoAdministracao(function(){
                    if(callback){
                        callback();
                    }
                });
            });
        };

        scope.getStatus = function (callback) {
            scope._status = [];
            service.getStatus().then(function (objectReturn) {
                scope._status = objectReturn.data;

                if(callback){
                    callback();
                }
            });
        };

        scope.getTipoAdministracao = function (callback) {
            scope._tipoAdministracao = [];
            service.getListaTipoAdministracao().then(function (objectReturn) {
                scope._tipoAdministracao = objectReturn.data;

                if(callback){
                    callback();
                }
            });
        };

        scope.novoRegistro = function (callback) {
            scope._registro = {};
            scope._registro.instituicaoParceira = '';
            scope._registro.status = null;
            scope._registro.tipoAdministracao = {};
            scope._registro.demandas = [];
            scope._registro.relatorios = [];

            scope._registroInicial = 1;
            scope.page = 1;
            scope._tamanhoLimite = 10;
            scope._optionTamanho = [10, 20];
            scope._maxRegistros = 10;
            scope._totalItems = 0;
            scope._filtros = {order: 'dtPrazo', orderBy: 'DESC', inicio: '', fim: ''};
            scope._filtros = {order: 'dtPrazo', orderBy: 'DESC', inicio: '', fim: ''};
            scope.demanda = '';

            scope.exibirSalvar = true;

            if (callback) {
                callback();
            }
        };

        scope.buscarDemandas = function (filtro) {
            scope._filtros.page = scope._registroInicial - 1;
            scope._filtros.size = scope._maxRegistros;

            scope._demandas = [];

            if (scope.demanda.length >= 3 && !filtro) {
                OrdemFiscalizacaoService.buscarDemandas(scope._filtros, scope.demanda).then(function (retorno) {
                    if (retorno.data.length > 0) {
                        scope.showDemandas = true;

                        scope._demandas = retorno.data || [];

                        var hoje = new Date();
                        for (var i = 0; i < scope._demandas.length; i++) {


                            scope.handleDtPrazoSelected(scope._demandas[i], function () {
                                if (hoje >= scope.dtPrazoAlert && hoje <= scope._demandas[i].dtPrazo) {
                                    scope._demandas[i].alertIcon = true;
                                } else if (hoje > scope._demandas[i].dtPrazo) {
                                    scope._demandas[i].alertDate = true;
                                }
                            });
                        }

                        scope._totalItems = retorno.data.totalElements;
                        return 0;
                    } else{
                        ComumFactory.mostrarMensagemError('Nenhuma demanda disponível!');
                    }
                });
            } else if (filtro) {
                service.buscarAllDemandasByUF(scope._filtros).then(function (retorno) {

                    if (retorno.data) {
                        scope.showDemandas = true;
                        scope._demandas = retorno.data.content || [];

                        var hoje = new Date();
                        for (var i = 0; i < scope._demandas.length; i++) {


                            scope.handleDtPrazoSelected(scope._demandas[i], function () {
                                if (hoje >= scope.dtPrazoAlert && hoje <= scope._demandas[i].dtPrazo) {
                                    scope._demandas[i].alertIcon = true;
                                } else if (hoje > scope._demandas[i].dtPrazo) {
                                    scope._demandas[i].alertDate = true;
                                }
                            });
                        }

                        scope._totalItems = retorno.data.totalElements;
                        return 0;
                    }
                });
            }
        };

        scope.handleDtPrazoSelected = function (demanda, callback) {
            var days = 15;
            var date = new Date(demanda.dtPrazo);
            scope.dtPrazoAlert = date.setTime(date.getTime() - (days * 24 * 60 * 60 * 1000));
            scope.dtPrazoAlert = new Date(scope.dtPrazoAlert);

            if (callback) {
                callback();
            }
        };


        scope.filtrar = function () {
            scope._filtros.page = scope.registroInicial - 1;
            scope._filtros.size = scope.tamanhoLimite;

            if (scope._filtros.orderBy === 'ASC') {
                scope._filtros.orderBy = 'DESC';
                scope._filtros.order = scope._filtros.order.replace('-', '');
            } else {
                scope._filtros.orderBy = 'ASC';
                scope._filtros.order = scope._filtros.order.replace('-', '');
            }

            service.buscarAllDemandasByUF(scope._filtros).then(function (retorno) {
                scope._demandas = retorno.data.content || [];

                var hoje = new Date();
                for (var i = 0; i < scope._demandas.length; i++) {
                    scope.handleDtPrazoSelected(scope._demandas[i], function () {
                        if (hoje >= scope.dtPrazoAlert && hoje <= scope._demandas[i].dtPrazo) {
                            scope._demandas[i].alertIcon = true;
                        } else if (hoje > scope._demandas[i].dtPrazo) {
                            scope._demandas[i].alertDate = true;
                        }
                    });
                }

                scope.totalItems = retorno.data.totalElements;
            });

            if (scope._filtros.orderBy === 'DESC') {
                scope._filtros.order = '-' + scope._filtros.order;
            }
        };

        scope.adicionarDemanda = function (demanda) {
            if (!scope._registro.demandas) {
                scope._registro.demandas = [];
            }
            if(!scope._registro.relatorios){
                scope._registro.relatorios = [];
            }

            for (var i in scope._registro.demandas) {


                if (scope._registro.demandas[i].id === demanda.id) {
                    ComumFactory.mostrarMensagemError('Demanda já adicionada!');
                    return;
                } else if (scope._registro.demandas[i].endereco.estado != demanda.endereco.estado) {
                    ComumFactory.mostrarMensagemError('Favor adicionar apenas demandas da mesma UF');
                    return;
                }
            }

            scope._registro.demandas.push(demanda);

            scope._demandas.splice(scope._demandas.indexOf(demanda), 1);

            // cria objeto relatorio fiscalizacao para essa demanda
            var relatorios = {};
            relatorios.demanda = demanda;
            relatorios.demandaExecutada = null;
            relatorios.fiscalizacaoSpu = null;
            relatorios.dataInicio = null;
            relatorios.dataFim = null;
            relatorios.arquivoConfirmacao = [];
            relatorios.arquivoRelatorio = [];

            scope._registro.relatorios.push(relatorios);




            scope.dadosDemanda = true;

        };

        /*
        *
        * Regra de negocio 14 - US035:  quando o usuario preencher o campo fiscalizacaoSPU com SIM ou NAO
        * para todas as demandas adicionadas na fiscalizacao, entao o sistema apresenta o botão Autuar;
        *
        */
    
        
        scope.showAtuarButton = function () {
            var exibe = true;

            angular.forEach(scope._registro.relatorios, function(value, key){
                if(!value.demandaExecutada || (value.demandaExecutada && value.fiscalizacaoSpu === null)){
                    exibe = false;
                }
            });

            scope.exibirAutuar = exibe;
        };



        scope.changeDemandaExecutada = function (fiscalizacao) {
            var index;

            if(fiscalizacao.demandaExecutada){
                index = scope._registro.relatorios.indexOf(fiscalizacao);

                scope._registro.relatorios[index].justificativa = null;

            }else{

              //  var exibirAutuar = false;

                index = scope._registro.relatorios.indexOf(fiscalizacao);

                scope._registro.relatorios[index].arquivoRelatorio = [];
                scope._registro.relatorios[index].fiscalizacaoSpu = null;
                scope._registro.relatorios[index].dataInicio = null;
                scope._registro.relatorios[index].dataFim = null;

                scope.showAtuarButton();

            /*    angular.forEach(scope._registro.relatorios, function(value, key){
                    if(value.demandaExecutada){
                        exibirAutuar = true;
                    }
                });


                if(!exibirAutuar){
                    scope.exibirAutuar = false;
                } */
            }
        };


        scope.visualizarDemanda = function (demandaId) {
            var url = $state.href('fiscalizacao.demanda.consultar', {id: demandaId});
            window.open(url, '_blank');
        };

        scope.removerDemanda = function (demandas) {
            var confirm = $mdDialog.confirm()
            .title('Atenção!')
            .textContent('Ao remover a demanda todas as informações e arquivos relacionados à ela que foram preenchidos aqui serão perdidos. Deseja confirmar a remoção dessa demanda?')
            .ok('SIM')
            .cancel('NÃO');

            $mdDialog.show(confirm).then(function () {
                // excluir arquivos inseridos
                if(scope._demandas){
                    scope._demandas.push(demandas);
                }
                
                scope._registro.demandas.splice(scope._registro.demandas.indexOf(demandas), 1);

                angular.forEach(scope._registro.relatorios, function(value, key){
                    if(value.demanda.id == demandas.id ){
                        scope._registro.relatorios.splice(scope._registro.relatorios.indexOf(value), 1);
                    }
                });

                if(!(scope._registro.relatorios.length > 0)){
                    scope.dadosDemanda = false;
                }
            });

        };



        scope.adicionarArquivoRelatorio = function (files, relatorios) {
            scope.apenasLeitura = true;
            scope.realizandoUpload = true;
            var index = scope._registro.relatorios.indexOf(relatorios);

            if (files && files.length) {
                for (var i = 0; i < files.length; i++) {
                    service.upload(files[i]).then(function (objectReturn) {
                        if (!scope._registro.relatorios.arquivoRelatorio) {
                            scope._registro.relatorios.arquivoRelatorio = [];
                        }

                        scope._registro.relatorios[index].arquivoRelatorio.push(objectReturn.data);


                        scope.apenasLeitura = false;
                        scope.realizandoUpload = false;
                    });
                }
            }

        };

        scope.adicionarArquivoDocumento = function (files, relatorios) {
            scope.apenasLeitura = true;
            scope.realizandoUpload = true;
            var index = scope._registro.relatorios.indexOf(relatorios);

            if (files && files.length) {
                for (var i = 0; i < files.length; i++) {
                    service.upload(files[i]).then(function (objectReturn) {
                        if (!scope._registro.relatorios.arquivoConfirmacao) {
                            scope._registro.relatorios.arquivoConfirmacao = [];
                        }

                        scope._registro.relatorios[index].arquivoConfirmacao.push(objectReturn.data);


                        scope.apenasLeitura = false;
                        scope.realizandoUpload = false;
                    });
                }
            }

        };



        scope.excluirArquivoRelatorioAgenteExterno = function (index, relatorios) {
            var indexRelatorio = scope._registro.relatorios.indexOf(relatorios);

            scope._registro.relatorios[indexRelatorio].arquivoRelatorio.splice(index, 1);


        };

        scope.excluirArquivoConfirmacaoSolicitacao = function (index, relatorios) {
            var indexRelatorio = scope._registro.relatorios.indexOf(relatorios);

            scope._registro.relatorios[indexRelatorio].arquivoConfirmacao.splice(index, 1);


        };

        scope.validaDataFinal = function (dataFim, fiscalizacao) {
            if(fiscalizacao.dataInicio){
                var fim = angular.copy(dataFim);
                var inicial = angular.copy(fiscalizacao.dataInicio);
                inicial = inicial.getTime();
                if (fim) {
                    fim = fim.getTime();
                }

                if (fim < inicial) {

                    var indexRelatorio = scope._registro.relatorios.indexOf(fiscalizacao);

                    scope._registro.relatorios[indexRelatorio].dataFim = null;
                    scope._registro.relatorios[indexRelatorio].dataInicio = null;

                    ComumFactory.mostrarMensagemError('Período de fiscalização inválido. ');
                }
            }


        };


        scope.validaFormulario = function (registro, autuar) {
            if (scope._registro.demandas.length > 0) {

                if ($scope.formRegistro.$valid) {
                    if (registro.demandas && registro.demandas.length) {
                       return true;
                    } else {
                        ComumFactory.mostrarMensagemError('Selecione pelo menos uma demanda!');
                        return false;
                    }

                } else {
                    var isDataInvalida = false;
                    angular.forEach($scope.formRegistro.$error.valid, function (erro, key) {
                        if (erro.$name === 'dataInicio' || erro.$name === 'dataFim' ) {
                            isDataInvalida = true;
                        }
                    });
                    if (isDataInvalida) {
                        ComumFactory.mostrarMensagemError('Data(s) inválida(s)!');
                    } else {
                        if(autuar){
                            ComumFactory.mostrarMensagemError('Para autuar no processo é necessário o preenchimento de todos os campos obrigatórios e upload de todos os arquivos solicitados');
                        }else{
                            ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
                        }
                        

                    }
                    return false;
                }
            } else {
                ComumFactory.mostrarMensagemError('Demandas não informadas!');
            }
        };

        scope.persistir = function (registro) {

            //$scope.formRegistro.$setSubmitted();
            var fisc = angular.copy(registro);


                angular.forEach( fisc.relatorios, function(value, key){
                    value.cpfResponsavelAutuacao = $rootScope.usuarioLogado.cpf;
                    value.nomeResponsavelAutuacao = $rootScope.usuarioLogado.nome;

                    if(value.arquivoConfirmacao.length <= 0){
                        value.arquivoConfirmacao = null;
                    }


                  if(value.arquivoRelatorio.length <= 0){
                        value.arquivoRelatorio = null;
                  }


                });

                fisc.demandas = null;

                if (fisc.id) {
                    service.update(fisc).then(function () {
                        $state.go('fiscalizacao.ordemFiscalizacao.listar');
                    });
                } else {
                    service.create(fisc).then(function () {
                        $state.go('fiscalizacao.ordemFiscalizacao.listar');
                    });
                }
            
        }

        scope.autuar = function (registro) {

            $scope.formRegistro.$setSubmitted();

            var fisc = angular.copy(registro);

            if (scope.validaFormulario(fisc, true)) {

                angular.forEach( fisc.relatorios, function(value, key){
                    value.cpfResponsavelAutuacao = $rootScope.usuarioLogado.cpf;
                    value.nomeResponsavelAutuacao = $rootScope.usuarioLogado.nome;

                    if(value.arquivoConfirmacao.length <= 0){
                        value.arquivoConfirmacao = null;
                    }


                   if(value.arquivoRelatorio.length <= 0){
                        value.arquivoRelatorio = null;
                   }
                });

                fisc.demandas = null;

                service.autuar(fisc).then(function () {
                        $state.go('fiscalizacao.ordemFiscalizacao.listar');
                });

            }
        }



        scope.init();
    }


})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('OrdemFiscalizacaoDetalharController', ['$scope', '$rootScope', '$filter', '$state', '$stateParams', '$mdDialog', 'IntegradorService', 'OrdemFiscalizacaoService', 'ComumFactory', 'MapaServiceFiscalizacao', '$timeout', controller]);

    function controller($scope, $rootScope, $filter, $state, $stateParams, $mdDialog, integradorService, service, ComumFactory, MapaServiceFiscalizacao, $timeout) {
        var scope = this;
        scope.openModal = openModal;
        scope.closeModal = closeModal;
        window.mdSelectOnKeyDownOverride = function(event) { event.stopPropagation(); };
        scope.revalidarModal = 'revalidarModal';


        scope.init = function () {
            scope.apenasLeitura = true;

            //Inicia as listas de origem e destinos de estados e municípios
            scope._estadosOrigem = [];
            scope._municipiosOrigem = [];
            scope._estadosDestino = [];
            scope._municipiosDestino = [];

            scope.headerPagina(function(){
                scope.getUsuario(function () {
                    scope.carregarListas(function(){
                        if ($stateParams.id) {
                            if($state.current.name.indexOf('consultar') !== -1 || $state.current.name === "fiscalizacao.ordemFiscalizacao.revalidar" || $state.current.name === "fiscalizacao.ordemFiscalizacao.validar"){
                                scope.findBy($stateParams.id, function(){
                                    if(scope._registro.origem && scope._registro.destinos){
                                        scope.destinosCopy = angular.copy(scope._registro.destinos);
                                        scope.getMunicipiosOrigem(scope._registro.ufOrigem, function(){
                                            scope.validaMunicipiosDestino(scope._registro.ufDestino, function(){
												var ids = (scope.destinosCopy ? scope.destinosCopy : []).map(function(des){ return des.id});
												scope._registro.destinos = scope._municipiosDestino.filter(function(m){ return (ids.indexOf(m.id) > -1) });
												scope.apenasLeitura = true;
                                            });
                                        });
                                    }else{
                                        scope.apenasLeitura = true;
                                    }
                                });
                            }else{
                                scope.findBy($stateParams.id, function(){
                                    scope.gerarOrdemFiscalizacaoViaMapa(function(){
                                        if(scope._registro.origem && scope._registro.destinos){
                                            scope.destinosCopy = angular.copy(scope._registro.destinos);
                                            scope.getMunicipiosOrigem(scope._registro.ufOrigem, function(){
                                                scope.validaMunicipiosDestino(scope._registro.ufDestino, function(){
													scope.apenasLeitura = false;
													var ids = (scope.destinosCopy ? scope.destinosCopy : []).map(function(des){ return des.id});
													scope._registro.destinos = scope._municipiosDestino.filter(function(m){ return (ids.indexOf(m.id) > -1) });
                                                });
                                            });
                                        }
                                        scope.apenasLeitura = false;
                                    });
                                });
                            }
                        } else {
                            scope.novoRegistro(function(){
                                scope.exibirBotaoRascunho = true;
                                scope.exibirBotaoGerar = true;
                                scope.apenasLeitura = false;
                            });
                        }
                        if ($state.current.name.indexOf('incluir') !== -1 || $state.current.name.indexOf('mapa') !== -1) {
                            scope.gerarOrdemFiscalizacaoViaMapa(function(){
                                scope.apenasLeitura = false;
                            });
                        }
                    });
                });
            });
        };

        scope.carregarListas = function(callback){
            scope.getStatus(function(){
                scope.getEstadosOrigem(function(){
                    scope.getEstadosDestino(function(){
                        scope.buscarFiscais(function(){
                            if(callback){
                                callback();
                            }
                        });
                    });
                });
            });
        };

        scope.novoRegistro = function (callback) {
            scope._registro = {};
            scope._registro.inicioDeslocamento = '';
            scope._registro.fimDeslocamento = '';
            scope._registro.ufOrdem = 'GO';
            scope._registro.demandas = [];
            scope._registro.fiscais = [];
            scope._registro.transporteFluvial = false;
            scope._registro.transporteTerrestre = false;
            scope._registro.transporteAereo = false;

            scope._registroInicial = 1;
            scope.page = 1;
            scope._tamanhoLimite = 10;
            scope._optionTamanho = [10, 20];
            scope._maxRegistros = 10;
            scope._totalItems = 0;
            scope._filtros = {order: 'dtPrazo', orderBy: 'DESC', inicio: '', fim: ''};
            scope.demanda = '';

            scope.exibirBotaoRascunho = false;
            scope.exibirBotaoGerar = false;

            scope.exibirEditar = false;
            scope.exibirCancelar = false;
            scope.exibirValidar = false;
            scope.exibirVisualizar = false;

            scope.isEditar = false;
            scope.isCadastro = false;
            scope.isValidar = false;
            scope.isVisualizar = false;

            scope.minDateInicio = new Date();

            if (callback) {
                callback();
            }
        };

        scope.headerPagina = function (callback) {
            if ($state.current.name.indexOf('listar') === -1) {
                if ($state.current.name.indexOf('incluir') !== -1) {
                    scope.tituloPagina = 'Cadastrar Ordem de Fiscalização';
                    scope.show = true;
                } else if ($state.current.name.indexOf('consultar') !== -1) {
                    scope.tituloPagina = 'Visualizar Ordem de Fiscalização';
                    scope.show = false;
                } else if ($state.current.name.indexOf('alterar') !== -1) {
                    scope.tituloPagina = 'Alterar Ordem de Fiscalização';
                    scope.show = true;
                } else if ($state.current.name === "fiscalizacao.ordemFiscalizacao.validar") {
                    scope.tituloPagina = 'Validar Ordem de Fiscalização';
                    scope.tituloBotaovalidacao = 'Validar Ordem de Fiscalização';
                    scope.tituloModalValidacao = "Deseja validar Ordem de Fiscalização?";
                    scope.show = false;
                } else if ($state.current.name === "fiscalizacao.ordemFiscalizacao.revalidar") {
                    scope.tituloPagina = 'Revalidar Ordem de Fiscalização';
                    scope.tituloBotaovalidacao = 'Revalidar Ordem de Fiscalização';
                    scope.tituloModalValidacao = "Deseja revalidar esta Ordem de Fiscalização?";
                    scope.show = false;
                }
            }

            if(callback){
                callback();
            }
        };

        scope.findBy = function (registroId, callback) {
            service.findBy(registroId).then(function (objectResponse) {
                scope._registro = objectResponse.data;
                scope.validaStatus(scope._registro.status);

                scope.minDateInicio = new Date();

                if (scope._registro.inicio) {
                    scope._registro.inicio = new Date(scope._registro.inicio);
                    scope.handleInicioFiscalizacaoSelected(scope._registro.inicio);
                } else {
                    scope._registro.inicio = '';
                }

                if (scope._registro.fim) {
                    scope._registro.fim = new Date(scope._registro.fim);
                    scope.handleFimFiscalizacaoSelected(scope._registro.fim);
                } else {
                    scope._registro.fim = '';
                }

                if (scope._registro.inicioDeslocamento) {
                    scope._registro.inicioDeslocamento = new Date(scope._registro.inicioDeslocamento);
                } else {
                    scope._registro.inicioDeslocamento = '';
                }

                if (scope._registro.fimDeslocamento) {
                    scope._registro.fimDeslocamento = new Date(scope._registro.fimDeslocamento);
                } else {
                    scope._registro.fimDeslocamento = '';
                }

                for (var i = 0; i <  scope._registro.demandas.length; i++) {
                    if(scope._registro.demandas[i].justificativaDemandaOf){
                        scope._registro.demandas[i].justificativaHint = scope._registro.demandas[i].justificativaDemandaOf;
                    }else{
                        scope._registro.demandas[i].justificativaHint = scope._registro.demandas[i].justificativa;
                    }
                }

                if(callback){
                    callback();
                }
            });
        };

        scope.validaStatus = function (status) {
            if ($state.current.name.indexOf('consultar') === -1) {
                if ($state.current.name === "fiscalizacao.ordemFiscalizacao.validar" || $state.current.name === "fiscalizacao.ordemFiscalizacao.revalidar") {
                    scope.exibirBotaoValidar = true;
                } else {
                    switch (status) {
                        case 'AGUARDANDO_VALIDACAO':
                            scope.exibirBotaoGerar = true;
                            break;
                        default:
                            scope.exibirBotaoRascunho = true;
                            scope.exibirBotaoGerar = true;
                            break;
                    }
                }

            }
        };

        scope.validaFormulario = function (registro) {
            if (scope._registro.demandas.length > 0) {
                if (scope._registro.viagem && $scope.formRegistro.$error.mindate || $scope.formRegistro.$error.maxdate) {
                    ComumFactory.mostrarMensagemError('Data para deslocamento selecionada inválida!');
                    return false;
                }

                if ($scope.formRegistro.$valid) {
                    if (scope._registro.viagem) {
                        if (!scope._registro.transporteFluvial && !scope._registro.transporteTerrestre && !scope._registro.transporteAereo) {
                            ComumFactory.mostrarMensagemError('Campo(s) de Tipo de Transporte não preenchido(s)');
                            return false;
                        }
                    }

                    if (registro.demandas && registro.demandas.length) {

                        if (registro.fiscais && registro.fiscais.length) {
                            return true;
                        } else {
                            ComumFactory.mostrarMensagemError('Selecione pelo menos um fiscal!');
                            return false;
                        }

                    } else {
                        ComumFactory.mostrarMensagemError('Selecione pelo menos uma demanda!');
                        return false;
                    }

                } else {
                    var isDataInvalida = false;
                    angular.forEach($scope.formRegistro.$error.valid, function (erro, key) {
                        if (erro.$name === 'inicio' || erro.$name === 'fim' || $scope.formRegistro.$error.mindate || $scope.formRegistro.$error.maxdate) {
                            isDataInvalida = true;
                        }
                    });
                    if (isDataInvalida) {
                        ComumFactory.mostrarMensagemError('Data(s) inválida(s)!');
                    } else {
                        ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');

                    }
                    return false;
                }
            } else {
                ComumFactory.mostrarMensagemError('Demandas não informadas!');
            }
        };

        scope.persistir = function (registro) {
            $scope.formRegistro.$setSubmitted();
            var of = angular.copy(registro);

            if (scope.validaFormulario(of)) {
                of.cpfUsuario = $rootScope.usuarioLogado.cpf;
                of.nomeUsuario = $rootScope.usuarioLogado.nome;
                of.ufOrdem = 'GO';

                if (of.id) {
                    service.update(of).then(function () {
                        $state.go('fiscalizacao.ordemFiscalizacao.listar');
                    });
                } else {
                    service.create(of).then(function () {
                        $state.go('fiscalizacao.ordemFiscalizacao.listar');
                    });
                }
            }
        };

        scope.validar = function (registro) {
            if ($state.current.name === "fiscalizacao.ordemFiscalizacao.validar") {
                var confirm = $mdDialog.confirm()
                    .title('Atenção!')
                    .textContent(scope.tituloModalValidacao)
                    .ok('Confirmar')
                    .cancel('Cancelar');

                $mdDialog.show(confirm).then(function () {
                    if ($state.current.name === "fiscalizacao.ordemFiscalizacao.validar") {
                        if (scope.validaFormulario(registro)) {
                            service.validar(registro).then(function () {
                                $state.go('fiscalizacao.ordemFiscalizacao.listar');
                            });
                        }
                    }
                });

            } else {
                scope.showModalRevalidar(registro);
            }
        };


        scope.showModalRevalidar = function (registro) {
            scope._registro = registro;
            $mdDialog.show({
                controller: function () {
                    return scope;
                },
                controllerAs: 'scope',
                templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/listar/view/ordemFiscalizacao-modal-revalidacao.tmpl.html',
                parent: angular.element(document.body),
                autoWrap: true,
                disableParentScroll: true,
                focusOnOpen: true,
                clickOutsideToClose: true,
                fullscreen: true

            });
        };

        scope.rejeitar = function (registro) {
            console.log("rejeitar",registro);
                       service.rejeitar(registro).then(function () {
                        scope.closeDialog();
                        $state.go('fiscalizacao.relatorio.incluir');
                    });
        };

        scope.revalidar = function (registro) {
            console.log("revalidar");
            service.revalidar(registro).then(function () {
                scope.closeDialog();
                $state.go('fiscalizacao.ordemFiscalizacao.listar');
            });
        };


        scope.closeDialog = function () {
             $mdDialog.hide();
        };

        scope.scrollToTopRevalidar = function () {
            var elmnt = document.getElementById('revalidarModal');
            elmnt.scrollTop = 0;
        };



        function openModal(id){
            ModalService.Open(id);
        }

        function closeModal(id){
            ModalService.Close(id);
        }

        scope.buscarDemandas = function (filtro) {
            scope._filtros.page = scope._registroInicial - 1;
            scope._filtros.size = scope._maxRegistros;

            scope._demandas = [];
            if (scope.demanda.length >= 3 && !filtro) {
                service.buscarDemandas(scope._filtros, scope.demanda).then(function (retorno) {
                    if (retorno.data) {
                        scope._demandas = retorno.data || [];

                        var hoje = new Date();
                        for (var i = 0; i < scope._demandas.length; i++) {

                            if(scope._demandas[i].justificativaDemandaOf){
                                scope._demandas[i].justificativaHint = scope._demandas[i].justificativaDemandaOf;
                            }else{
                                scope._demandas[i].justificativaHint = scope._demandas[i].justificativa;
                            }

                            scope.handleDtPrazoSelected(scope._demandas[i], function () {
                                if (hoje >= scope.dtPrazoAlert && hoje <= scope._demandas[i].dtPrazo) {
                                    scope._demandas[i].alertIcon = true;
                                } else if (hoje > scope._demandas[i].dtPrazo) {
                                    scope._demandas[i].alertDate = true;
                                }
                            });
                        }

                        scope._totalItems = retorno.data.totalElements;
                        return 0;
                    }
                });
            } else if (filtro) {
                service.buscarAllDemandasByUF(scope._filtros).then(function (retorno) {
                    if (retorno.data) {
                        scope._demandas = retorno.data.content || [];

                        var hoje = new Date();
                        for (var i = 0; i < scope._demandas.length; i++) {

                            if(scope._demandas[i].justificativaDemandaOf){
                                scope._demandas[i].justificativaHint = scope._demandas[i].justificativaDemandaOf;
                            }else{
                                scope._demandas[i].justificativaHint = scope._demandas[i].justificativa;
                            }

                            scope.handleDtPrazoSelected(scope._demandas[i], function () {
                                if (hoje >= scope.dtPrazoAlert && hoje <= scope._demandas[i].dtPrazo) {
                                    scope._demandas[i].alertIcon = true;
                                } else if (hoje > scope._demandas[i].dtPrazo) {
                                    scope._demandas[i].alertDate = true;
                                }
                            });
                        }

                        scope._totalItems = retorno.data.totalElements;
                        return 0;
                    }
                });
            }
        };

        scope.filtrar = function () {
            scope._filtros.page = scope.registroInicial - 1;
            scope._filtros.size = scope.tamanhoLimite;

            if (scope._filtros.orderBy === 'ASC') {
                scope._filtros.orderBy = 'DESC';
                scope._filtros.order = scope._filtros.order.replace('-', '');
            } else {
                scope._filtros.orderBy = 'ASC';
                scope._filtros.order = scope._filtros.order.replace('-', '');
            }

            service.buscarAllDemandasByUF(scope._filtros).then(function (retorno) {
                scope._demandas = retorno.data.content || [];

                var hoje = new Date();
                for (var i = 0; i < scope._demandas.length; i++) {
                    scope.handleDtPrazoSelected(scope._demandas[i], function () {
                        if (hoje >= scope.dtPrazoAlert && hoje <= scope._demandas[i].dtPrazo) {
                            scope._demandas[i].alertIcon = true;
                        } else if (hoje > scope._demandas[i].dtPrazo) {
                            scope._demandas[i].alertDate = true;
                        }
                    });
                }

                scope.totalItems = retorno.data.totalElements;
            });

            if (scope._filtros.orderBy === 'DESC') {
                scope._filtros.order = '-' + scope._filtros.order;
            }
        };

        scope.buscarFiscais = function (callback) {
            scope._fiscais = [];
            service.buscarFiscais().then(function (retorno) {
                scope._fiscais = retorno.data;
                scope.totalItems = retorno.data.length;

                if(callback){
                    callback();
                }
            });
        };

        scope.adicionarFiscal = function (fiscal) {
            if (!scope._registro.fiscais) {
                scope._registro.fiscais = [];
            }
            if (fiscal.id) {
                for (var i in scope._registro.fiscais) {
                    if (scope._registro.fiscais[i].id === fiscal.id) {
                        ComumFactory.mostrarMensagemError('Fiscal já adicionado!');
                        return 0;
                    }
                }
                scope._registro.fiscais.push(angular.copy(fiscal));
                scope.registro.fiscal = {};
            }
        };

        scope.removerFiscal = function (fiscais) {
            scope._registro.fiscais.splice(scope._registro.fiscais.indexOf(fiscais), 1);
        };

        scope.adicionarDemanda = function (demanda) {
            if (!scope._registro.demandas) {
                scope._registro.demandas = [];
            }

            for (var i in scope._registro.demandas) {

                if(scope._demandas[i].justificativaDemandaOf){
                    scope._demandas[i].justificativaHint = scope._demandas[i].justificativaDemandaOf;
                }else{
                    scope._demandas[i].justificativaHint = scope._demandas[i].justificativa;
                }

                if (scope._registro.demandas[i].id === demanda.id) {
                    ComumFactory.mostrarMensagemError('Demanda já adicionada!');
                    return;
                } else if (scope._registro.demandas[i].endereco.estado != demanda.endereco.estado) {
                    ComumFactory.mostrarMensagemError('Favor adicionar apenas demandas da mesma UF');
                    return;
                }
            }

            scope._registro.demandas.push(demanda);

            scope._registro.ufOrigem = scope._registro.demandas[0].endereco.estado;
            scope._registro.ufDestino = scope._registro.demandas[0].endereco.estado;
            scope._registro.uf = scope._registro.demandas[0].endereco.estado;

            scope.getMunicipios();
        };

        scope.visualizarDemanda = function (demandaId) {
            var url = $state.href('fiscalizacao.demanda.consultar', {id: demandaId});
            window.open(url, '_blank');
        };

        scope.removerDemanda = function (demandas) {
            scope._registro.demandas.splice(scope._registro.demandas.indexOf(demandas), 1);
        };

        scope.showModalDetalheHistorico = function (ev, historico) {
            scope._detalhes = historico.detalhes;
            $mdDialog.show({
                controller: function () {
                    return scope;
                },
                controllerAs: 'scope',
                templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/detalhar/views/ordemFiscalizacao-historico-modal.tmpl.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                autoWrap: false,
                disableParentScroll: true,
                focusOnOpen: true,
                clickOutsideToClose: true,
                fullscreen: true,
                onComplete: function () {
                    scope.scrollToTop();
                }
            });
        };

        scope.scrollToTop = function () {
            var elmnt = document.getElementById('modal');
            elmnt.scrollTop = 0;
        };

        scope.rascunho = function (registro) {
            scope.registro = angular.copy(registro);
            var of = angular.copy(registro);

            of.cpfUsuario = $rootScope.usuarioLogado.cpf;
            of.nomeUsuario = $rootScope.usuarioLogado.nome;
            of.ufOrdem = 'GO';

            service.rascunho(of).then(function () {
                $state.go('fiscalizacao.ordemFiscalizacao.listar');
            });
        };

        scope.getHistorico = function () {
            scope.buscandoHistorico = true;
            scope.mensagemHistorico = "Buscando Históricos!";
            scope._historicos = [];
            service.getHistorico($stateParams.id).then(function (objectReturn) {
                scope._historicos = objectReturn.data;

                if(scope._historicos.length ===  0){
                    scope.mensagemHistorico = "Nenhum resultado encontrado!";
                }

                scope.buscandoHistorico = false;
            });
        };

        scope.cancelModal = function () {
            $mdDialog.cancel();
        };

        scope.getUsuario = function (callback) {
            scope._usuario = {};
            service.getUsuario().then(function (objectReturn) {
                scope._usuario = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.getStatus = function (callback) {
            scope._status = [];
            service.getStatus().then(function (objectReturn) {
                scope._status = objectReturn.data;

                if(callback){
                    callback();
                }
            });
        };

        scope.gerarOrdemFiscalizacaoViaMapa = function (callback) {

            var result = MapaServiceFiscalizacao.getOrdemFiscalizacao(scope._registro.demandas);

            result.then(function(objectRequest){
                scope._registro.demandas = objectRequest;
            });

            $timeout(function () {
                MapaServiceFiscalizacao.limpaDemandas();
            });

            if(callback){
                callback();
            }
        };

        scope.getEstadosOrigem = function (callback) {
            integradorService.getEstados().then(function (objectReturn) {
                scope._usuario.jurisdicoes.forEach(function (juridicao) {
                    objectReturn.data.forEach(function (estado) {

                        if (juridicao === estado.sigla) {
                            scope._estadosOrigem.push(estado);
                        }
                    });
                });
                if (callback) {
                    callback();
                }
            });
        };

        scope.getMunicipiosOrigem = function (sigla, callback) {
            if (sigla) {
                integradorService.getMunicipios(sigla).then(function (objectReturn) {
                    scope._municipiosOrigem = $filter("orderbyAccentsName")(objectReturn.data);

                    if (callback) {
                        callback();
                    }
                });
            }
        };

        scope.getEstadosDestino = function (callback) {
            integradorService.getEstados().then(function (objectReturn) {
                scope._usuario.jurisdicoes.forEach(function (jurisdicao) {
                    objectReturn.data.forEach(function (estado) {
                        if (jurisdicao === estado.sigla) {
                            scope._estadosDestino.push(estado);
                        }
                    });
                });

                if (callback) {
                    callback();
                }
            });
        };

        scope.getMunicipiosDestino = function (sigla, callback) {
            if (sigla) {
                integradorService.getMunicipios(sigla).then(function (objectReturn) {
                    scope._municipiosDestino = $filter("orderbyAccentsName")(objectReturn.data);

                    if (callback) {
                        callback();
                    }
                });
            }
        };

        scope.validaMunicipiosDestino = function(origem, callback){
            scope.destinoCarregando = true;
            scope.brasilia = 1778;
            scope._registro.destinos = null;
            scope.getMunicipiosDestino(scope._registro.ufDestino, function(){
                for(var i = 0;i < scope._municipiosDestino.length; i++){
                    if(origem === scope._municipiosDestino[i].id & origem != scope.brasilia){
                        scope._municipiosDestino.splice(i, 1);
                    }
                }
				scope.destinoCarregando = false;

                if(callback){
                    callback();
                }
            });
        };

        scope.getMunicipios = function () {
            scope._municipiosOrigem = [];
            scope._municipiosDestino = [];

            if(scope._registro.viagem){
                scope.getMunicipiosOrigem(scope._registro.ufOrigem, function(){});
                scope.getMunicipiosDestino(scope._registro.ufOrigem, function(){});
                scope.validaQuantidadeMunicipiosDestino(false);
            }
        }

        scope.validaQuantidadeMunicipiosDestino = function(alert) {
            scope._municipiosDestino.map(function(value) {

                if(scope._registro.destinos.length >= 10){
                    var ids = (scope._registro.destinos ? scope._registro.destinos : []).map(function(des){ return des.id});

                    if(ids.indexOf(value.id) > -1){
                        value.checked = false;
                    }else{
                        value.checked = true;
                    }
                }else{
                    value.checked = false;
                }
            });
            if(scope._registro.destinos.length >= 10 && alert){
                ComumFactory.mostrarMensagemError('Atenção!! Somente é permitido selecionar até dez municipios.');
            }
        }



        scope.handleInicioFiscalizacaoSelected = function (dtInicio) {
            var days = 7;
            var date = new Date(dtInicio);
            var res = date.setTime(date.getTime() - (days * 24 * 60 * 60 * 1000));
            scope.dtMinInicial = new Date(res);
        };

        scope.handleFimFiscalizacaoSelected = function (dtFinal) {
            var days = 7;
            var date = new Date(dtFinal);
            var res = date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            scope.dtMaxFinal = new Date(res);
        };

        scope.handleDtPrazoSelected = function (demanda, callback) {
            var days = 15;
            var date = new Date(demanda.dtPrazo);
            scope.dtPrazoAlert = date.setTime(date.getTime() - (days * 24 * 60 * 60 * 1000));
            scope.dtPrazoAlert = new Date(scope.dtPrazoAlert);

            if (callback) {
                callback();
            }
        };

        scope.validaDataInicialDeslocamento = function (inicioDeslocamento) {
            if (scope._registro.inicio) {
                var inicio = angular.copy(scope._registro.inicio);
                var deslocamentoInicio = angular.copy(inicioDeslocamento);
                inicio = inicio.getTime();

                if (deslocamentoInicio) {
                    deslocamentoInicio = deslocamentoInicio.getTime();
                }

                if (deslocamentoInicio > inicio) {
                    scope._registro.inicioDeslocamento = null;
                    ComumFactory.mostrarMensagemError('O início do período do deslocamento deve ser menor ou igual ao início de período de fiscalização!');
                }
            } else {
                scope._registro.inicioDeslocamento = null;
                ComumFactory.mostrarMensagemError('É necessário selecionar uma data inicial do período de fiscalização!');
            }
        };

        scope.validaDataFinalDeslocamento = function (fimDeslocamento) {
            if (scope._registro.fim) {
                var fim = angular.copy(scope._registro.fim);
                var fimDeslocamento = angular.copy(fimDeslocamento);
                fim = fim.getTime();

                if (fimDeslocamento) {
                    fimDeslocamento = fimDeslocamento.getTime();
                }

                if (fimDeslocamento < fim) {
                    scope._registro.fimDeslocamento = null;
                    ComumFactory.mostrarMensagemError('A Data final do deslocamento deve ser maior ou igual ao período do fim da fiscalização!');
                }
            } else {
                scope._registro.fimDeslocamento = null;
                ComumFactory.mostrarMensagemError('É necessário selecionar uma data final do período de fiscalização!');
            }
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').factory('SuspenderService', ["$http", function ($http) {

        var baseUrl = '/fiscalizacao/api/suspender';

        var suspenderAutoEmbargo = function (registro) {
            return $http.post(baseUrl + '/autoEmbargo', registro);
        };

        var alterarStatusPendenteVistoria = function (registro) {
            return $http.post(baseUrl + '/status/pendenteVistoria', registro);
        };

        var autuarSei = function (registro) {
            return $http.post(baseUrl + '/autuarSei', registro);
        };


        return {
            suspenderAutoEmbargo: suspenderAutoEmbargo,
            alterarStatusPendenteVistoria: alterarStatusPendenteVistoria,
            autuarSei: autuarSei
        };
    }]);
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').factory('SancaoService', ["$http", "Upload", function ($http, Upload) {

        var baseUrl = '/fiscalizacao/api/sancao';
        var uploadUrl = '/fiscalizacao/api/arquivo';

        var salvar = function (registro) {
            return $http.post(baseUrl + '/salvar', registro);
        };

        var findBy = function (registroId) {
            return $http.get(baseUrl + '/' + registroId);
        };

        var getDocumento = function (documentoId) {
            return $http.get(baseUrl + '/documento/' + documentoId);
        };

        var findDocumentosByFilter = function (registro) {
            return $http.post(baseUrl
                + "/filtro?page=" + registro.page
                + "&size=" + registro.size
                + "&sort=" + registro.order + "," + registro.orderBy, registro)
        };

        var getTiposDocumento = function () {
            return $http.get(baseUrl + '/tipos-documento');
        };

        var getStatusDocumento = function () {
            return $http.get(baseUrl + '/status-documento');
        };

        var getTiposNotificacao = function () {
            return $http.get(baseUrl + '/tipos-notificacao');
        };

        var getTiposLogradouro = function () {
            return $http.get(baseUrl + '/tipos-logradouro');
        };

        var getDocumentoIdentificacao = function () {
            return $http.get(baseUrl + '/documento-identificacao');
        };

        var getStatusSancao = function (tipoSancao) {
            var url = baseUrl + '/status-sancao';
            if(tipoSancao) {
                url = url + '?tipoSancao=' + tipoSancao;
            }
            return $http.get(url);
        };

        var consultarDocumento = function (consultarDocumentoSei) {

            return $http.get(baseUrl + "/consultar-documento/" + consultarDocumentoSei.protocoloDocumento);
        };

        var upload = function($file){
            return Upload.upload({
                url: uploadUrl + '/upload',
                data: {
                    file: $file
                }
            });
        };

        return {
            salvar:salvar,
            findBy:findBy,
            getDocumento: getDocumento,
            findDocumentosByFilter: findDocumentosByFilter,
            getTiposDocumento:getTiposDocumento,
            getStatusDocumento:getStatusDocumento,
            getTiposNotificacao:getTiposNotificacao,
            getTiposLogradouro:getTiposLogradouro,
            getDocumentoIdentificacao:getDocumentoIdentificacao,
            getStatusSancao:getStatusSancao,
            consultarDocumento:consultarDocumento,
            upload:upload
        };
    }]);
})();

(function () {'use strict';
    angular.module('su-fiscalizacao')
        .controller('SancaoController', ['$scope', '$state', '$element', '$stateParams', 'SancaoService', 'ComumFactory', 'IntegradorService', '$mdDialog','SuspenderService', controller]);

    function controller($scope, $state, $element, $stateParams, service, ComumFactory, integradorService, $mdDialog,suspender) {
        var scope = this;

        scope.registroInicial = 1;
        scope.page = 1;
        scope.tamanhoLimite = 10;
        scope.optionTamanho = [10, 20, 50];
        scope.maxRegistros = 10;
        scope.totalItems = 0;

        scope.init = function () {
            scope.apenasLeitura = true;
            scope.statusSomenteLeitura = true;
            scope.buscaStatus = '';
            scope.habilitaBotaoAutuar = false;
            scope.habilitaBotaoSalvar = false;

            scope._filtro = {
                order: 'dataEmissao',
                orderBy: 'ASC',
                dataEmissao: '',
                dataEntrega: '',
                uf: '',
                tipoDocumento: '',
                statusSancao: []
            };

            scope._documento = {};
            scope.tipoEnvio = null;
            scope._usuario = {};

            scope._estados = [];
            scope._registros = [];
            scope._statusSancao = {};
            scope._statusSancaoIndice = {};
            scope._statusSancaoParaSelecionar = [];
            scope._statusSancaoSelecionados = [];

            scope.getTiposDocumento(function () {
                scope.getEstados(function () {
                    scope.getStatusSancao(function () {
                        scope.getDocumentosAtivos(function () {
                            scope.apenasLeitura = false;
                        });
                    });
                });
            });

            scope.initDataAutoEmbargo();

        };

        scope.getStatusSancao = function(callback) {
            service.getStatusSancao()
                .then(function(response) {
                    scope._statusSancaoIndice = response.data;
                });
            
            if(callback) {
                callback();
            }
        }

        scope.initDataAutoEmbargo = function (documento) {
            // Inicializacao modal dados para suspernder auto de embargo

            scope.suspenderEmbargo = {};

            scope.suspenderEmbargo.infracaoCessadaShow = false;
            scope.suspenderEmbargo.showQuestao4 = false;
            scope.suspenderEmbargo.statusAlterado = '';

            scope.suspenderEmbargo.documentoSeiFinal = undefined;


            // pergunta 1
            scope.suspenderEmbargo.recursoAutuadoDeferido = undefined;
            scope.suspenderEmbargo.autoEmbargoAnulado = undefined;
            scope.suspenderEmbargo.documentoSeiEmbargoAnulado = undefined;
            scope.suspenderEmbargo.documentoSeiEmbargoNaoAnulado = undefined;

            // pergunta 2
            scope.suspenderEmbargo.indicioInfracaoParalisada = undefined;
            scope.suspenderEmbargo.novaVistoria = undefined;
            scope.suspenderEmbargo.arquivo = undefined;
            scope.suspenderEmbargo.justificativaMotivacao = undefined;
            scope.suspenderEmbargo.infracaoCessada = undefined;

            // pergunta 3
            scope.suspenderEmbargo.liminarDecisaoJudicialCobranca = undefined;
            scope.suspenderEmbargo.documentoSeiDecisaoJudicial = undefined;

            // pergunta 4
            scope.suspenderEmbargo.decisaoJudicialFavoravelUniao = undefined;
            scope.suspenderEmbargo.docSeiJudicialFavoravelUniao = undefined;
            scope.suspenderEmbargo.docSeiJudicialNaoFavoravelUniao = undefined;

            // pergunta 5
            scope.suspenderEmbargo.analisadoDestinacaoSuperintendente = undefined;
            scope.suspenderEmbargo.docSeiAnalisadoDestinacaoSuperintendente = undefined;


            // apresentacao da infracao
            scope.suspenderEmbargo.apresentaInfracaoCessada = false;

            // auto embargo para suspensão
            scope.documentoAutoEmbargo = documento;
        }

        scope.limparFiltros = function () {
            scope._filtro = {
                order: 'dataEmissao',
                orderBy: 'ASC',
                dataEmissao: '',
                dataEntrega: '',
                uf: '',
                tipoDocumento: '',
                statusSancao: []
            };

            scope.filtrar();
        };

        scope.paginar = function() {
            scope._filtro.page = scope.registroInicial - 1;
            scope._filtro.size = scope.tamanhoLimite;

            service.findDocumentosByFilter(scope._filtro).then(function (objectReturn) {
                scope._registros = objectReturn.data.content || [];
                scope.totalItems = objectReturn.data.totalElements;
            });
        }

        scope.filtrar = function () {
            if($scope.formFiltro.$valid){
                scope._filtro.page = 0;

                service.findDocumentosByFilter(scope._filtro).then(function (objectReturn) {
                    scope._registros = objectReturn.data.content || [];
                    scope.totalItems = objectReturn.data.totalElements;
                });
            }else{
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
            }
        };

        /**
         * Obtém uma lista de tipos de documento
         */
        scope.getTiposDocumento = function(callback){
            service.getTiposDocumento().then(function (objectReturn) {
                scope._tiposDocumento = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtem uma lista de estados
         * @param callback
         */
        scope.getEstados = function (callback) {
            integradorService.getEstados().then(function (objectReturn) {
                scope._estados = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtem uma lista de documentos com status ATIVO
         * @param callback
         */
        scope.getDocumentosAtivos = function(callback) {
            var filtro = {
                order: 'dataEmissao',
                orderBy: 'DESC',
                statusSancao: ['ATIVO'],
                page : scope.registroInicial - 1,
                size : scope.tamanhoLimite
            };

            scope._filtro = angular.copy(filtro);

            service.findDocumentosByFilter(filtro).then(function(objectReturn) {
                scope._registros = objectReturn.data.content;
                scope.totalItems = objectReturn.data.totalElements;

                if(callback) {
                    callback();
                }
            });
        }

        /**
         * Atualiza lista de status da sancao
         */
        scope.atualizarSeletorSancaoStatus = function() {
            if(scope._filtro.tipoDocumento) {
                scope.statusSomenteLeitura = true;
                var tipoSancao = scope._filtro.tipoDocumento;
                scope._statusSancao = {};
                scope._filtro.statusSancao = [];
                service.getStatusSancao(tipoSancao)
                    .then(function(statusSancaoResponse) {
                        scope._statusSancao = statusSancaoResponse.data;
                        scope._statusSancaoParaSelecionar = Object.values(scope._statusSancao);
                        scope._statusSancaoSelecionados = [];
                        scope.buscaStatus = '';
                        scope.statusSomenteLeitura = false;
                    });
            }
        }

        scope.atualizarFiltroStatus = function() {
            var statusSancaoPorDescricao = {};
            for(var statusSancao in scope._statusSancao) {
                statusSancaoPorDescricao[
                    scope._statusSancao[statusSancao]
                ] = statusSancao;
            }

            scope._filtro.statusSancao = [];
            for(var i=0; i< scope._statusSancaoSelecionados.length; i++) {
                var statusSancaoDescricao = scope._statusSancaoSelecionados[i];
                var _statusSancao = statusSancaoPorDescricao[statusSancaoDescricao];
                scope._filtro.statusSancao.push(_statusSancao);
            }
        }

        scope.questionarioRespondido = function(){
            if($scope.formSuspenderEmbargo.$valid) {

                scope.verificaNovoStatus();

                if (!scope.habilitaBotaoAutuar) {
                    suspender.suspenderAutoEmbargo(scope.suspenderEmbargo).then(function () {
                        scope.resetDados();
                        scope.modalHide();
                    });
                } else {
                    suspender.autuarSei(scope.suspenderEmbargo).then(function () {
                        scope.resetDados();
                        scope.modalHide();
                    });
                }
            }else{
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
            }
        }

        scope.resetDados = function () {
            scope.habilitaBotaoAutuar = false;
            scope.habilitaBotaoSalvar = false;
            scope.documentoAutoEmbargo = null;
        }

        scope.preencheDocumentoSei = function () {
            if (scope.suspenderEmbargo.documentoSeiEmbargoAnulado != undefined) {
                scope.suspenderEmbargo.documentoSeiFinal = scope.suspenderEmbargo.documentoSeiEmbargoAnulado;
            } else if (scope.suspenderEmbargo.documentoSeiEmbargoNaoAnulado != undefined) {
                scope.suspenderEmbargo.documentoSeiFinal = scope.suspenderEmbargo.documentoSeiEmbargoNaoAnulado;
            } else if (scope.suspenderEmbargo.documentoSeiDecisaoJudicial != undefined) {
                scope.suspenderEmbargo.documentoSeiFinal = scope.suspenderEmbargo.documentoSeiDecisaoJudicial
            } else if (scope.suspenderEmbargo.docSeiJudicialFavoravelUniao != undefined) {
                scope.suspenderEmbargo.documentoSeiFinal = scope.suspenderEmbargo.docSeiJudicialFavoravelUniao
            } else if (scope.suspenderEmbargo.docSeiJudicialNaoFavoravelUniao != undefined) {
                scope.suspenderEmbargo.documentoSeiFinal = scope.suspenderEmbargo.docSeiJudicialNaoFavoravelUniao
            } else if (scope.suspenderEmbargo.docSeiAnalisadoDestinacaoSuperintendente != undefined) {
                scope.suspenderEmbargo.documentoSeiFinal = scope.suspenderEmbargo.docSeiAnalisadoDestinacaoSuperintendente
            }
        }

        scope.verificaNovoStatus = function () {
            if (scope.suspenderEmbargo.autoEmbargoAnulado == true) {
                scope.suspenderEmbargo.statusAlterado = 'CANCELADO';

            } else if (scope.suspenderEmbargo.autoEmbargoAnulado == false) {
                scope.suspenderEmbargo.statusAlterado = 'INATIVO';
            }

            if (scope.suspenderEmbargo.novaVistoria == false) {
                scope.suspenderEmbargo.statusAlterado = 'INATIVO';
            }

            if (scope.suspenderEmbargo.infracaoCessada == true) {
                scope.suspenderEmbargo.statusAlterado = 'INATIVO';
            }

            if (scope.suspenderEmbargo.liminarDecisaoJudicialCobranca == true
                && scope.documentoAutoEmbargo.statusSancao !== 'PARALISADO') {
                scope.suspenderEmbargo.statusAlterado = 'PARALISADO';
            } else {
                if (scope.suspenderEmbargo.decisaoJudicialFavoravelUniao == true) {
                    scope.suspenderEmbargo.statusAlterado = 'ATIVO';
                } else {
                    scope.suspenderEmbargo.statusAlterado = 'INATIVO';
                }

                if(scope.suspenderEmbargo.analisadoDestinacaoSuperintendente == true){
                    scope.suspenderEmbargo.statusAlterado = 'INATIVO';
                }

                if (scope.suspenderEmbargo.recursoAutuadoDeferido == false
                    && scope.suspenderEmbargo.indicioInfracaoParalisada == false
                    && scope.suspenderEmbargo.liminarDecisaoJudicialCobranca == false
                    && scope.suspenderEmbargo.analisadoDestinacaoSuperintendente == false) {
                    scope.suspenderEmbargo.statusAlterado = '';
                }
            }
        }

        scope.modalHide = function() {
            $mdDialog.hide();
        };

        scope.cancel = function() {
            $mdDialog.cancel();
        };

        scope.addArquivoComprobatorio = function (file) {
            service.upload(file[0]).then(function (objectReturn) {
                scope.suspenderEmbargo.arquivo = objectReturn.data;
            });
            // scope.suspenderEmbargo.arquivo = file[0];
        };

        scope.showDialog = function(ev) {
            $mdDialog.show({
                contentElement: '#myDialog',
                // parent: angular.element(document.body),
                targetEvent: ev,
                disableParentScroll: false,
                clickOutsideToClose: false

            });
        };

        scope.hide = function() {
            $mdDialog.hide();
        }

        scope.modal = function (documento) {
            scope.initDataAutoEmbargo(documento);

            if(documento.statusSancao == 'PENDENTE_DE_VISTORIA'){
                scope.suspenderEmbargo.recursoAutuadoDeferido = false;
                scope.suspenderEmbargo.indicioInfracaoParalisada = true;
                scope.suspenderEmbargo.apresentaInfracaoCessada = true;
                scope.suspenderEmbargo.novaVistoria = true;
                scope.suspenderEmbargo.infracaoCessadaShow = true;
            }

            if(documento.statusSancao == 'PARALISADO'){
                scope.suspenderEmbargo.showQuestao4 = true;
            }

            scope.suspenderEmbargo.idDocumento = documento.id;

            scope.showDialog();
        }

        scope.changeState = function (documento, evento) {

            if (documento.tipoDocumento == 'AUTO_DE_EMBARGO') {
                scope.modal(documento);
            } else if (documento.tipoDocumento == 'NOTIFICACAO') {
                $state.go('fiscalizacao.gerenciadorInfracao.suspenderNotificacao', {documentoId: documento.id, demandaId: documento.relatorio.demanda.id});
            }else if (!documento.sancao) {
                $state.go('fiscalizacao.gerenciadorInfracao.suspenderCobranca', {documentoId: documento.id});
            } else {
                $state.go('fiscalizacao.gerenciadorInfracao.detalharCobranca', {
                    id: documento.sancao.id,
                    documentoId: documento.id
                });
            }
        };

        scope.consultarDocumento = function(protocoloDocumento){
            scope.consultarDocumentoSei = {};
            scope.consultarDocumentoSei.protocoloDocumento = protocoloDocumento;

            if(scope.consultarDocumentoSei.protocoloDocumento.length === 7){
                scope.apenasLeitura = true;
                service.consultarDocumento(scope.consultarDocumentoSei).then(function(objectResponse){
                    scope.suspenderEmbargo.documentoSei = objectResponse.data;
                    scope.habilitaBotoesModal();
                   }, function(){
                    ComumFactory.mostrarMensagemError('Documento não encontrado!');
                    scope.suspenderEmbargo.documentoSei = {};
                    scope.habilitaBotaoAutuar = false;
                });
            }
        };


        scope.habilitaBotoesModal = function () {
            if (scope.suspenderEmbargo.decisaoJudicialFavoravelUniao
                || !scope.suspenderEmbargo.decisaoJudicialFavoravelUniao
                && scope.suspenderEmbargo.decisaoJudicialFavoravelUniao != undefined) {
                scope.habilitaBotaoSalvar = true;
            } else {
                scope.habilitaBotaoAutuar = true;
            }
        }

        $element.find('#input-busca-status').on('keydown', function(ev) {
            ev.stopPropagation();
        });

        scope.disableSalvar = function () {
            if(!scope.suspenderEmbargo.recursoAutuadoDeferido
                && scope.suspenderEmbargo.recursoAutuadoDeferido != undefined
                && !scope.suspenderEmbargo.indicioInfracaoParalisada
                && scope.suspenderEmbargo.indicioInfracaoParalisada != undefined
                && !scope.suspenderEmbargo.liminarDecisaoJudicialCobranca
                && scope.suspenderEmbargo.liminarDecisaoJudicialCobranca != undefined
                && !scope.suspenderEmbargo.analisadoDestinacaoSuperintendente
                && scope.suspenderEmbargo.analisadoDestinacaoSuperintendente != undefined){
                scope.habilitaBotaoSalvar = false;
            }else{
                scope.habilitaBotaoSalvar = true;
            }

        }

        scope.regrasEmbargoQuestao1 = function () {
            if (scope.suspenderEmbargo.recursoAutuadoDeferido) {
                scope.suspenderEmbargo.indicioInfracaoParalisada = undefined;
                scope.suspenderEmbargo.novaVistoria = undefined;
                scope.suspenderEmbargo.liminarDecisaoJudicialCobranca = undefined;
                scope.suspenderEmbargo.documentoSeiDecisaoJudicial = undefined;
                scope.suspenderEmbargo.infracaoCessada = undefined;
                scope.suspenderEmbargo.justificativaMotivacao = undefined;
                scope.suspenderEmbargo.analisadoDestinacaoSuperintendente = undefined;
                scope.suspenderEmbargo.docSeiAnalisadoDestinacaoSuperintendente = undefined;
            }else{
                scope.suspenderEmbargo.autoEmbargoAnulado = undefined;
                scope.suspenderEmbargo.documentoSeiEmbargoAnulado = undefined;
                scope.suspenderEmbargo.documentoSeiEmbargoNaoAnulado = undefined;
            }
        }


        scope.regraQuestaoEmbargoAnulado = function () {
            if (scope.suspenderEmbargo.autoEmbargoAnulado) {
                scope.suspenderEmbargo.documentoSeiEmbargoNaoAnulado = undefined;
            } else {
                scope.suspenderEmbargo.documentoSeiEmbargoAnulado = undefined;
            }
        }

        scope.regrasEmbargoQuestao2 = function () {
            if(scope.suspenderEmbargo.indicioInfracaoParalisada){
                scope.suspenderEmbargo.liminarDecisaoJudicialCobranca = undefined;
                scope.suspenderEmbargo.documentoSeiDecisaoJudicial = undefined;
                scope.suspenderEmbargo.analisadoDestinacaoSuperintendente = undefined;
                scope.suspenderEmbargo.docSeiAnalisadoDestinacaoSuperintendente = undefined;
            }else{
                scope.suspenderEmbargo.novaVistoria = undefined;
                scope.suspenderEmbargo.arquivo = undefined;
                scope.suspenderEmbargo.justificativaMotivacao = undefined;
            }
        }

        scope.regrasEmbargoQuestao3 = function () {
            if (scope.suspenderEmbargo.liminarDecisaoJudicialCobranca) {
                scope.suspenderEmbargo.analisadoDestinacaoSuperintendente = undefined;
                scope.suspenderEmbargo.docSeiAnalisadoDestinacaoSuperintendente = undefined;
            } else {
                scope.suspenderEmbargo.documentoSeiDecisaoJudicial = undefined
            }
        }

        scope.novaVistoria = function(){
            suspender.alterarStatusPendenteVistoria(scope.suspenderEmbargo)
                .then(function () {
                    scope.hide()
                    $state.go('fiscalizacao.demanda.incluir');
                });
        }

        $scope.$watchGroup(
            [
                'scope.suspenderEmbargo.justificativaMotivacao',
                'scope.suspenderEmbargo.arquivo'
            ], function () {
                scope.habilitaBotaoAutuarP2();
            });

        $scope.$watchGroup(
            [
                'scope.suspenderEmbargo.analisadoDestinacaoSuperintendente',
                'scope.suspenderEmbargo.recursoAutuadoDeferido',
                'scope.suspenderEmbargo.indicioInfracaoParalisada',
                'scope.suspenderEmbargo.liminarDecisaoJudicialCobranca'
            ], function () {
                scope.disableSalvar();
        });

        scope.habilitaBotaoAutuarP2 = function () {
            if (scope.suspenderEmbargo.arquivo != undefined
                && scope.suspenderEmbargo.justificativaMotivacao != undefined
                && scope.suspenderEmbargo.justificativaMotivacao != '') {
                scope.habilitaBotaoAutuar = true;
            } else {
                // scope.habilitaBotaoAutuar = false;
            }
        }

        scope.habilitaSuspenderEmbargo = function (statusSancao, tipoSancao) {
            if (tipoSancao == 'AUTO_DE_INFRACAO') {
                if (statusSancao == 'PENDENTE_DE_ENTREGA'
                    || statusSancao == 'ENTREGUE'
                    || statusSancao == 'AUTUADO') {
                    return false;
                } else {
                    return true;
                }
            } else if (tipoSancao == 'AUTO_DE_EMBARGO' || tipoSancao == 'NOTIFICACAO') {
                if (statusSancao == 'PENDENTE_DE_ENTREGA'
                    || statusSancao == 'ENTREGUE'
                    || statusSancao == 'AUTUADO'
                    || statusSancao == 'INATIVO') {
                    return false;
                } else {
                    return true;
                }
            }
        }

        scope.init();

    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').factory('RecursoService', ["$http", function ($http) {

        var baseUrl = '/fiscalizacao/api/recurso';

        var salvar = function (registro) {
            return $http.post(baseUrl + '/salvar', registro);
        };

        var findBy = function (registroId) {
            return $http.get(baseUrl + '/' + registroId);
        };

        var findDocumentosByFilter = function (documento) {
            return $http.post(baseUrl + "/filtro?page=" + documento.page + "&size=" + documento.size + "&sort=" + documento.order + "," + documento.orderBy, documento);
        };

        var getTiposDocumento = function () {
            return $http.get(baseUrl + '/tipos-documento');
        };

        var getTiposNotificacao = function () {
            return $http.get(baseUrl + '/tipos-notificacao');
        };

        var getTiposLogradouro = function () {
            return $http.get(baseUrl + '/tipos-logradouro');
        };

        var getDocumentoIdentificacao = function () {
            return $http.get(baseUrl + '/documento-identificacao');
        };

        var getDocumento = function (documentoId) {
            return $http.get(baseUrl + '/documento/' + documentoId);
        };

        return {
            salvar:salvar,
            findBy:findBy,
            findDocumentosByFilter: findDocumentosByFilter,
            getTiposDocumento:getTiposDocumento,
            getTiposNotificacao: getTiposNotificacao,
            getTiposLogradouro:getTiposLogradouro,
            getDocumentoIdentificacao:getDocumentoIdentificacao,
            getDocumento:getDocumento
        };
    }]);
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('RecursoController', ['$scope', '$state', '$stateParams', 'RecursoService', 'ComumFactory', 'IntegradorService', '$mdDialog', controller]);

    function controller($scope, $state, $stateParams, service, ComumFactory, integradorService, $mdDialog) {
        var scope = this;

        scope.init = function () {
            scope.apenasLeitura = true;

            scope._filtro = {
                order: 'dataEmissao',
                orderBy: 'ASC',
                dataEmissao: '',
                dataEntrega: '',
                uf: '',
                tipoDocumento: ''
            };

            scope._documento = {};
            scope.tipoEnvio = null;
            scope._usuario = {};

            scope._estados = [];
            scope._registros = [];

            scope.getTiposDocumento(function(){
                scope.getEstados(function(){
                    scope.apenasLeitura = false;
                });
            });
        };

        scope.limparFiltros = function () {
            scope._filtro = {
                order: 'dataEmissao',
                orderBy: 'ASC',
                dataEmissao: '',
                dataEntrega: '',
                uf: '',
                tipoDocumento: ''
            };

            scope.filtrar();
        };

        scope.filtrar = function (filtro) {
            if($scope.formFiltro.$valid){
                filtro.page = scope.registroInicial - 1;
                filtro.size = scope.tamanhoLimite;

                if (filtro.orderBy === 'ASC') {
                    filtro.orderBy = 'DESC';
                    filtro.order = filtro.order.replace('-', '');
                } else {
                    filtro.orderBy = 'ASC';
                    filtro.order = filtro.order.replace('-', '');
                }

                service.findDocumentosByFilter(filtro).then(function (objectReturn) {
                    scope._registros = objectReturn.data.content || [];
                    scope.totalItems = objectReturn.data.totalElements;
                });

                if (filtro.orderBy === 'DESC') {
                    filtro.order = '-' + filtro.order;
                }
            }else{
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
            }
        };

        /**
         * Obtém uma lista de tipos de documento
         */
        scope.getTiposDocumento = function(callback){
            service.getTiposDocumento().then(function (objectReturn) {
                scope._tiposDocumento = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtem uma lista de estados
         * @param callback
         */
        scope.getEstados = function (callback) {
            integradorService.getEstados().then(function (objectReturn) {
                scope._estados = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        scope.openControlePrazoModal = function(documento){
            $mdDialog.show({
                controller: 'ControlePrazoController as scope',
                templateUrl: 'scripts-fiscalizacao/pages/gerenciadorInfracao/gerenciarRecurso/controlePrazo/view/controle-prazo.html',
                parent: angular.element(document.body),
                targetEvent: event,
                clickOutsideToClose: true,
                fullscreen: true,
                autoWrap: false,
                disableParentScroll: true,
                focusOnOpen: true,
                locals: {
                    documento: documento,
                    filtro: scope._filtro
                },
                onComplete: function () {
                    scope.documento = angular.copy(documento);

                    scope._filtro = angular.copy(scope._filtro)
                }
            });
        };

        scope.openEspelhoAutoModal = function(documentoId){
            $mdDialog.show({
                controller: 'DetalheAutoController as scope',
                templateUrl: 'scripts-fiscalizacao/pages/gerenciadorInfracao/gerenciarRecurso/detalheAuto/view/detalhe-auto.html',
                parent: angular.element(document.body),
                targetEvent: event,
                clickOutsideToClose: true,
                fullscreen: true,
                autoWrap: false,
                disableParentScroll: true,
                focusOnOpen: true,
                locals: {
                    documentoId: documentoId,
                }
            });
        };

        scope.init();
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').factory('EntregaDocumentoService', ["$http", "Upload", function ($http, Upload) {

        var baseUrlDocumento = '/fiscalizacao/api/documentos';
        var uploadUrl = '/fiscalizacao/api/arquivo';

        var findDocumentosByFilter = function (documento) {
            return $http.post(baseUrlDocumento + "/filtro?page=" + documento.page + "&size=" + documento.size + "&sort=" + documento.order + "," + documento.orderBy, documento);
        };

        var upload = function($file){
            return Upload.upload({
                url: uploadUrl + '/upload',
                data: {
                    file: $file
                }
            });
        };

        var getTiposDocumento = function () {
            return $http.get(baseUrlDocumento + '/tipos-documento');
        };

        var entregaDocumento = function (documento) {
            return $http.post(baseUrlDocumento + "/entrega-documento", documento);
        };

        return {
            findDocumentosByFilter: findDocumentosByFilter,
            upload:upload,
            getTiposDocumento:getTiposDocumento,
            entregaDocumento: entregaDocumento
        };
    }]);
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('EntregaDocumentoController', ['$scope', '$state', '$stateParams', 'EntregaDocumentoService', 'ComumFactory', 'IntegradorService', controller]);

    function controller($scope, $state, $stateParams, service, ComumFactory, integradorService) {
        var scope = this;

        scope.init = function () {
            scope._filtro = {
                order: 'dataEmissao',
                orderBy: 'ASC',
                dataEmissao: '',
                dataEntrega: '',
                uf: '',
                tipoDocumento: ''
            };

            scope._documento = {};
            scope.tipoEnvio = null;
            scope._usuario = {};

            scope._estados = [];
            scope._registros = [];

            scope.getTiposDocumento(function(){
               scope.getEstados(function(){});
            });
        };

        scope.initEntregaDocumento = function(registro){
            scope.documentoSelecionado = true;
            scope._registro = registro;
            scope._documento.id = registro.id;
        };

        scope.limparFiltros = function () {
            scope._filtro = {
                order: 'dataEmissao',
                orderBy: 'ASC',
                dataEmissao: '',
                dataEntrega: '',
                uf: '',
                tipoDocumento: ''
            };

            scope.filtrar();
        };

        scope.filtrar = function (filtro) {
            filtro.page = scope.registroInicial - 1;
            filtro.size = scope.tamanhoLimite;
            filtro.statusDocumento = "PENDENTE_DE_ENTREGA";

            if (filtro.orderBy === 'ASC') {
                filtro.orderBy = 'DESC';
                filtro.order = filtro.order.replace('-', '');
            } else {
                filtro.orderBy = 'ASC';
                filtro.order = filtro.order.replace('-', '');
            }

            service.findDocumentosByFilter(filtro).then(function (objectReturn) {
                scope._registros = objectReturn.data.content || [];
                scope.totalItems = objectReturn.data.totalElements;
            });

            if (filtro.orderBy === 'DESC') {
                filtro.order = '-' + filtro.order;
            }
        };

        /**
         * Limpa o registro para um novo cadastro
         */
        scope.limparRegistro = function(){
          scope._documento = {};

            $scope.formRegistro.$setPristine();
            $scope.formRegistro.$setUntouched();
        };

        /**
         * Realiza o upload do comprovante de entrega do documento
         * @param file
         */
        scope.adicionarComprovanteEntrega = function (file) {
            service.upload(file[0]).then(function (objectReturn) {
                scope._documento.comprovanteDeEntrega = objectReturn.data;

                scope.apenasLeitura = false;
            });
        };

        /**
         * Obtém uma lista de tipos de documento
         */
        scope.getTiposDocumento = function(callback){
            service.getTiposDocumento().then(function (objectReturn) {
                scope._tiposDocumento = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtem uma lista de estados
         * @param callback
         */
        scope.getEstados = function (callback) {
            integradorService.getEstados().then(function (objectReturn) {
                scope._estados = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        scope.persistirRegistro = function(documento){
            if($scope.formRegistro.$valid){
                service.entregaDocumento(documento).then(function(){
                    scope._documento = {};

                    $scope.formRegistro.$setPristine();
                    $scope.formRegistro.$setUntouched();
                    scope.documentoSelecionado = false;

                    scope.filtrar(scope._filtro);
                });
            }else{
                ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
            }
        };

        scope.limparComprovanteEntrega = function(){
            scope._documento.comprovanteDeEntrega = {};
        };

        scope.init();
    }
})();

(function() {
    'use strict';
    angular.module('su-fiscalizacao').controller('DemandaMapaController', ['$scope', 'DemandaService', 'MapaServiceFiscalizacao', 'ciMapaLayersFactoryFiscalizacao', 'ciMapaViewFactoryFiscalizacao', 'NotificadorServiceFiscalizacao', 'MAPA_CONSTANTS_FISCALIZACAO', '$timeout', 'ComumFactory', '$state', 'mFactory', '$mdSidenav', '$rootScope', controller]);

    function controller($scope, service, MapaServiceFiscalizacao, ciMapaLayersFactoryFiscalizacao, ciMapaViewFactoryFiscalizacao, NotificadorServiceFiscalizacao, MAPA_CONSTANTS_FISCALIZACAO, $timeout, ComumFactory, $state, mFactory, $mdSidenav, $rootScope) {
        var scope = this;
        scope._registroInicial = 1;
        scope.page = 1;
        scope._tamanhoLimite = 10;
        scope._optionTamanho = [10, 20];
        scope._maxRegistros = 10;
        scope._totalItems = 0;
        scope._filtros = { order: 'id', orderBy: 'ASC', inicio: '', fim: '' };
        scope.filtros = {};
        scope.demandasAdicionadas = [];
        scope.ano = new Date().getFullYear();
        scope.isOpen = false;

        scope.init = function() {
            scope.carregarMapa = false;
            scope.novoRegistro(function() {
                $timeout(function() {
                    scope.buscar(scope.filtros);
                    scope.btSetaEsquerda = true;
                });

            });
            scope.collapsed = true;

            scope.addDemanda();

        };

        scope.novoRegistro = function(callback) {

            ciMapaLayersFactoryFiscalizacao.init();
            ciMapaViewFactoryFiscalizacao.init();

            scope.carregarMapa = true;
            if (callback) {
                $timeout(function() {
                    callback();
                });
            }
            scope.filtros.disponivel = false;
            scope.filtros.vinculada = false;
            scope.filtros.executada = false;
            scope.filtros.emExecucaoAgenteExterno = false;
            scope.filtros.executadoAgenteExterno = false;

            $timeout(function() {
                scope.filtros.dtPrazoIni = new Date(new Date().setFullYear(new Date().getFullYear(), 0, 1));
                scope.filtros.dtPrazoFim = new Date(new Date().setFullYear(new Date().getFullYear(), 11, 30));
            });
        };

        function getListaCoordenadas(demandas) {
            var listaCoordenadas = [];
            for (var i = 0; i < demandas.length; i++) {
                listaCoordenadas.push({
                    coordenadas: {
                        'coordinates': [demandas[i].endereco.latitude, demandas[i].endereco.longitude],
                        'type': 'Point'
                    },
                    demandaId: demandas[i].id,
                    status: demandas[i].status,
                    tipoMarcador: getCorMarcador(demandas[i].dtPrazo)
                });
            }

            if (listaCoordenadas && listaCoordenadas.length > 0) {
                NotificadorServiceFiscalizacao.notify(scope, listaCoordenadas, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.MARCAR_LISTA_COORDENADAS);
            }

            function getCorMarcador(data) {
                var dtPrazo = data != null ? new Date(data) : null;
                var dataAtual = new Date();
                if (!dtPrazo) {
                    return 'AZUL';
                }
                if (dtPrazo < dataAtual) {
                    return 'VERMELHO';
                }
                if (getDifData() < 15) {
                    return 'AMARELO';
                }
                return 'AZUL';

                function getDifData() {
                    return (new Date().setHours(0, 0, 0, 0) - dtPrazo.setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24);
                }
            }
        }

        scope.getDemandasByUF = function(callback) {
            $timeout(function() {
                service.buscarAllDemandasByUF().then(function(retorno) {
                    if (retorno.data) {
                        scope._demandas = retorno.data;
                        getListaCoordenadas(retorno.data);
                        return 0;
                    }
                });
            });

            if (callback) {
                callback();
            }
        };

        scope.buscar = function(filtro) {
            if (!filtro.dtPrazoIni || !filtro.dtPrazoFim) {
                return
            }
            if (new Date(filtro.dtPrazoIni).getFullYear() !== new Date(filtro.dtPrazoFim).getFullYear()) {
                scope.ano = new Date(filtro.dtPrazoIni).getFullYear() + ' à ' + new Date(filtro.dtPrazoFim).getFullYear();
            } else {
                scope.ano = new Date(filtro.dtPrazoIni).getFullYear();
            }


            service.buscar(filtro).then(function(retorno) {
                if (retorno.data) {
                    scope._demandas = retorno.data;
                    getListaCoordenadas(retorno.data);
                    return 0;
                } else {
                    scope._demandas = [];
                }

            });
        };

        scope.addDemanda = function() {
            NotificadorServiceFiscalizacao.subscribe($scope, function(evento, escopo, demanda) {
                if (demanda.status === 'VINCULADA') {
                    ComumFactory.mostrarMensagemError('Demanda já vinculada!');
                    return
                }
                if (demanda.status === 'EXECUTADA') {
                    ComumFactory.mostrarMensagemError('Demanda já executada!');
                    return
                }
                if (scope.demandasAdicionadas) {
                    for (var i = 0; i < scope.demandasAdicionadas.length; i++) {
                        if (scope.demandasAdicionadas[i].id === demanda.id) {
                            ComumFactory.mostrarMensagemError('Demanda já adicionada!');
                            return;
                        }
                    }
                } else {
                    scope.demandasAdicionadas = [];
                }

                $timeout(function() {
                    for (var i = 0; i < scope._demandas.length; i++) {
                        if (scope._demandas[i].id === demanda.id) {
                            scope.demandasAdicionadas.push(scope._demandas[i]);
                        }
                    }
                });

                $timeout(function() {
                    scope.collapseMovimentacao();
                });
            }, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.ADICIONAR_DEMANDAS);

        };

        scope.removerDemanda = function(demanda) {
            $timeout(function() {
                scope.demandasAdicionadas.splice(scope.demandasAdicionadas.indexOf(demanda), 1);
            });
            $timeout(function() {
                scope.collapseMovimentacao();
            });
        };

        scope.gerarOrdemFiscalizacao = function(demandas) {
            MapaServiceFiscalizacao.gerarOrdemFiscalizacao(demandas);
            $state.go('fiscalizacao.ordemFiscalizacao.mapa');
        };

        scope.collapseMovimentacao = function() {
            if (scope.demandasAdicionadas.length > 0) {
                $timeout(function() {
                    $("#demo").addClass("collapse in");
                }, 500);
                scope.collapsed = false;
            } else {
                $timeout(function() {
                    $("#demo").removeClass("collapse in");
                    $("#demo").addClass("collapse");
                });
                scope.collapsed = true;
            }
        };

        // scope.abreNav = function () {
        //     $mdSidenav('buscaleft').open();
        //     scope.btSetaEsquerda = false;
        // };

        // scope.escondeNav = function () {
        //     $mdSidenav('buscaleft').close();
        //     mFactory.btSetaEsqueda.aberto = true;
        //     $rootScope.$broadcast('mostrarSeta');
        //     scope.btSetaEsquerda = true;
        // };
        scope.buildToggler = function() {
            scope.isOpen = !scope.isOpen;
        }

        scope.init();
    }
})();
(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('DemandaListarController', ['$scope', '$rootScope', '$filter', '$mdDialog', 'DemandaService', 'IntegradorService', 'ComumFactory', controller]);

    function controller($scope, $rootScope, $filter, $mdDialog, service, integradorService, ComumFactory) {

        var scope = this;

        scope.apenasLeitura = false;
        scope.registroInicial = 1;
        scope.tamanhoLimite = 20;
        scope.totalItems = 0;
        scope._estados = {};
        scope._status = {};
        scope.ocultaOf = true;

        scope.filtros = {
            order: 'id',
            orderBy: 'ASC',
            dtInicio: '',
            dtFim: ''
        };

        window.mdSelectOnKeyDownOverride = function(event) { event.stopPropagation(); };

        scope.init = function () {
            scope.getMotivacoes();
            scope.getAssuntos();
            scope.getStatus();
            scope.getUsuario(function () {
                scope.getJurisdicao(function () {
                    scope.limparFiltros();
                })
            });
        };

        scope.limparFiltros = function () {
            scope.filtros = {
                order: 'id',
                orderBy: 'ASC',
                dtInicio: '',
                dtFim: ''
            };
            scope.filtrar();
        };

        scope.filtrar = function () {
            if(!$scope.formRegistro.$valid) {
                return;
            }

            scope.filtros.page = scope.registroInicial - 1;
            scope.filtros.size = scope.tamanhoLimite;

            if (scope.filtros.orderBy === 'ASC') {
                scope.filtros.orderBy = 'DESC';
                scope.filtros.order = scope.filtros.order.replace('-', '');
            } else {
                scope.filtros.orderBy = 'ASC';
                scope.filtros.order = scope.filtros.order.replace('-', '');
            }

            if(scope.filtros.endereco && scope.filtros.endereco.municipio && scope.filtros.endereco.municipio.id)
                scope.filtros.endereco.idMunicipio = scope.filtros.endereco.municipio.id;
                
                if(scope.filtros.status === 'EXECUTADA_POR_AGENTE_EXTERNO' || scope.filtros.status === 'EM_EXECUCAO_POR_AGENTE_EXTERNO'){
                    scope.ocultaOf = false;
                }else{
                    scope.ocultaOf = true;
                }
                
            service.findByFilter(scope.filtros).then(function (retorno) {
                scope._registros = retorno.data.content || [];
                scope.totalItems = retorno.data.totalElements;
            });

            if (scope.filtros.orderBy === 'DESC') {
                scope.filtros.order = '-' + scope.filtros.order;
            }

        };

        scope.excluirRegistro = function (registro) {
            var confirm = $mdDialog.confirm()
                .title('Atenção!')
                .textContent('Tem certeza que deseja cancelar esta demanda?')
                .ok('Confirmar')
                .cancel('Cancelar');

            $mdDialog.show(confirm).then(function () {
                registro.cpfUsuario = $rootScope.usuarioLogado.cpf;
                registro.nomeUsuario = $rootScope.usuarioLogado.nome;
                service.excluirDemanda(registro).then(function () {
                    scope.filtrar();
                });
            });
        };

        scope.getAssuntos = function (callback) {
            scope._assuntos = {};
            service.getAssuntos().then(function (objectReturn) {
                scope._assuntos = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.getMunicipios = function (sigla, callback) {
            scope._municipios = [];
            if(scope.filtros.endereco)
                scope.filtros.endereco.municipio = null;

            if (sigla) {
                integradorService.getMunicipios(sigla).then(function (objectReturn) {
                    scope._municipios = $filter("orderbyAccentsName")(objectReturn.data)
                    if (callback) {
                        callback();
                    }
                });
            }
        };

        scope.getStatus = function (callback) {
            service.getStatus().then(function (objectReturn) {
                scope._status = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.getUsuario = function (callback) {
            scope._usuario = {};
            service.getUsuario().then(function (objectReturn) {
                scope._usuario = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.getMotivacoes = function (callback) {
            scope._motivacoes = {};
            service.getMotivacoes().then(function (objectReturn) {
                scope._motivacoes = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.getJurisdicao = function (callback) {
            if (!scope._usuario.jurisdicoes) {
                scope._estados = ['DF']
            } else {
                scope._estados = scope._usuario.jurisdicoes
            }
            callback();
        };

        scope.init();
    }

})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').controller('DemandaDetalharController', ['$scope', '$rootScope', '$state', '$stateParams', '$mdDialog', '$timeout', 'BuscarCpfServiceFiscalizacao', 'DemandaService', 'IntegradorService', 'GeoService', 'ComumFactory', 'CartografiaServiceFiscalizacao', 'ciMapaLayersFactoryFiscalizacao', 'ciMapaViewFactoryFiscalizacao', 'NotificadorServiceFiscalizacao', 'MAPA_CONSTANTS_FISCALIZACAO', '$window', controller]);

    function controller($scope, $rootScope, $state, $stateParams,  $mdDialog, $timeout, BuscarCpfServiceFiscalizacao, service, integradorService, geoService,ComumFactory, CartografiaServiceFiscalizacao, ciMapaLayersFactoryFiscalizacao, ciMapaViewFactoryFiscalizacao, NotificadorServiceFiscalizacao, MAPA_CONSTANTS_FISCALIZACAO, $window) {
        var scope = this;
        var vm = $scope;
		console.log('Teste Versão Dennys - Demanda');
        window.mdSelectOnKeyDownOverride = function(event) { event.stopPropagation(); };

        /**
         * Função responsável por iniciar o carregamento do mapa
         * @param callback
         */
        scope.iniciarMapa = function (callback) {
            ciMapaLayersFactoryFiscalizacao.init();
            ciMapaViewFactoryFiscalizacao.init();

            if (callback) {
                callback();
            }
        };


        vm.validarCpf = function (cpf) {
            if (cpf != null) {
                cpf = cpf.replace(/[^0-9]/g, '');
                if(cpf.length === 11){
                    vm.buscarPessoaCpf(cpf)
                }else if (cpf.length === 14){
                    vm.buscarRegistorCnpj(cpf)
                }else{
                    ComumFactory.mostrarMensagemError("CPF ou CNPJ inválido");
                }
            }
        };

        vm.buscarRegistorCnpj = function (cpf) {
            BuscarCpfServiceFiscalizacao.buscarCnpj(cpf).then(function (response) {
                scope.nomeAutomatico = true;
                scope.registro.responsavel.nome = response.data.nome;
            }, function () {
                cpf = '';
                scope.registro.responsavel.nome = ''
                scope.nomeAutomatico = false;
                ComumFactory.mostrarMensagemError("CNPJ não encontrado");
            })
        };

        vm.buscarPessoaCpf = function (cpf) {
            BuscarCpfServiceFiscalizacao.buscarPessoa(cpf).then(function (response) {
                scope.registro.responsavel.nome = response.data.nome;
                scope.nomeAutomatico = true;
            }, function () {
                cpf = '';
                scope.registro.responsavel.nome = ''
                scope.nomeAutomatico = false;
                ComumFactory.mostrarMensagemError("CPF não encontrado");
            })
        };





        /**
         * Verifica a ação que o usuário está realizando e inicia as funções comforme as permições
         * @param callback
         */
        scope.validarState = function(callback){
            if ($stateParams.id) {
                if($state.current.name.indexOf('consultar') !== -1){
                    scope.findBy($stateParams.id, function () {
                        var coordinates = [];
                        coordinates[0] = scope.registro.endereco.latitude;
                        coordinates[1] = scope.registro.endereco.longitude;

                        //Atualiza o ponto marcado no mapa através das coordenadas informadas no endereço
                        NotificadorServiceFiscalizacao.notify(scope, coordinates, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.ATUALIZAR_COORDENADAS_DO_PONTO);
                    });
                }else{
                    scope.findBy($stateParams.id, function () {
                        var coordinates = [];
                        coordinates[0] = scope.registro.endereco.latitude;
                        coordinates[1] = scope.registro.endereco.longitude;

                        //Atualiza o ponto marcado no mapa através das coordenadas informadas no endereço
                        NotificadorServiceFiscalizacao.notify(scope, coordinates, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.ATUALIZAR_COORDENADAS_DO_PONTO);

                        scope.apenasLeitura = false;
                    });

                }

            } else {
                if($state.current.name.indexOf('incluir') !== -1){
                    scope.novoRegistro(function () {
                        scope.carregarMapa = true;
                        scope.apenasLeitura = false;
                    });
                }else{
                    scope.novoRegistro(function () {
                        scope.showJustificativaDemandaOf = true;
                        scope.carregarMapa = true;
                        scope.registro.justificativaDemandaOf = angular.copy($window.localStorage.getItem("justificativaNovaDemanda"));
                        // $window.localStorage.removeItem("justificativaNovaDemanda");
                        scope.registro.idOrdemFiscalizacao = $stateParams.ofId;
                        scope.demandaOfExistente = true;

                        service.getOrdemFiscalizacaoByOfId($stateParams.ofId).then(function(objectResponse){
                            scope._ordemFiscalizacao = angular.copy(objectResponse.data);

                            scope.ufPermitida = scope._ordemFiscalizacao.demandas[0].endereco.estado;
                            scope.apenasLeitura = false;
                        });

                    });
                }
            }
        };

        scope.init = function () {
            scope.dadosEndereco = false;
            scope.carregarMapa = false;
            scope.apenasLeitura = true;

            scope.iniciarMapa(function () {
                scope.getMotivacoes(function () {
                    scope.getAssuntos(function () {
                        scope.getEstados(function () {
                            scope.getStatus(function () {
                                scope.getTipoLogradouro(function () {
                                    scope.getUsuario(function () {
                                        scope.getTiposImovel(function(){
                                            scope.getZoneamentos(function(){
                                                scope.validarState(function(){

                                                });
                                            });
                                        });
                                    });
                                });
                            });
                        });
                    });
                });
            });
        };

        scope.novoRegistro = function (callback) {
            scope.registro = {};
            iniciaEndereco(function(){
                scope._detalhes = [];
                scope._status = [];

                scope.registro.isEdesc = false;
                scope.ripPesquisado = false;
                scope.paf = null;
                scope.prazo = null;
                scope.processo = null;
                scope.buscarCEP = false;

                scope.registro.dtPrazo = '';

                if ($stateParams.id) {
                    scope.demandaId = angular.copy($stateParams.id);
                }

                if(callback) {
                    callback();
                }
            });
        };

        scope.findBy = function (registroId, callback) {
            service.findBy(registroId).then(function (objectResponse) {
                scope.registro = angular.copy(objectResponse.data);

                if(scope.registro.justificativaDemandaOf != null){
                    scope.showJustificativaDemandaOf = true;
                }

                if (!scope.registro.endereco.rip) {
                    scope.ripPesquisado = true;
                }

                scope.dadosEndereco = true;

                if (scope.registro.dtPrazo) {
                    scope.registro.dtPrazo = new Date(scope.registro.dtPrazo);
                } else {
                    scope.registro.dtPrazo = '';
                }

                scope.paf = !!(scope.registro.assunto);
                scope.processo = !scope.registro.processoAdministrativo;
                scope.prazo = !!(scope.registro.dtPrazo);
                scope.registro.isEdesc = !!(scope.registro.edesc) && !!(scope.registro.edesc.id);

                scope.registro.endereco.municipio = {};
                scope.registro.endereco.municipio.id = scope.registro.endereco.idMunicipio;
                scope.registro.endereco.municipio.nome = scope.registro.endereco.nomeMunicipio;

                //Popula a comboBox de motivação com o valor do registro
                if (scope.registro.motivacao && scope.registro.motivacao.id) {
                    for (var i = 0; i < scope._motivacoes.length; i++) {
                        if (scope._motivacoes[i].id === scope.registro.motivacao.id) {
                            scope.registro.motivacao = scope._motivacoes[i];
                            break;
                        }
                    }
                }

                //Popula a comboBox de assunto com o valor do registro
                if (scope.registro.assunto && scope.registro.assunto.id) {
                    for (var k = 0; k < scope._assuntos.length; k++) {
                        if (scope._assuntos[k].id === scope.registro.assunto.id) {
                            scope.registro.assunto = scope._assuntos[k];
                            break;
                        }
                    }
                }

                //Popula a comboBox de tipo de imóvel com o valor do registro
                if (scope.registro.tipoImovel && scope.registro.tipoImovel.id) {
                    for (var y = 0; y < scope._tiposImovel.length; y++) {
                        if (scope._tiposImovel[y].id === scope.registro.tipoImovel.id) {
                            scope.registro.tipoImovel = scope._tiposImovel[y];
                            break;
                        }
                    }
                }

                //Popula a comboBox de zoneamento com o valor do registro
                if (scope.registro.endereco.zoneamento && scope.registro.endereco.zoneamento.id) {
                    for (var l = 0; l < scope._zoneamentos.length; l++) {
                        if (scope._zoneamentos[l].id === scope.registro.endereco.zoneamento.id) {
                            scope.registro.endereco.zoneamento = scope._zoneamentos[l];
                            break;
                        }
                    }
                }

                //Popula a comboBox de município com o valor do registro
                scope.getMunicipios(scope.registro.endereco.estado, function(){
                    for (var a = 0; a < scope._municipios.length; a++) {
                        if (scope._municipios[a].id === scope.registro.endereco.idMunicipio) {
                            scope.registro.endereco.municipio = scope._municipios[a];
                            break;
                        }
                    }
                });

                //Popula a comboBox de edesc com o valor do registro
                if (objectResponse.data.edesc && objectResponse.data.edesc.id) {
                    for (var v = 0; v < scope._nucleoEdesc.length; v++) {
                        if (objectResponse.data.edesc.id === scope._nucleoEdesc[v].id) {
                            scope.registro.edesc = scope._nucleoEdesc[v];
                            break;
                        }
                    }
                }

                scope.carregarMapa = true;

                if (callback) {
                    callback();
                }
            }, function (error) {
                ComumFactory.mostrarMensagemError(error.data.message);
                window.history.back();
            });
        };

        /**
         * Função responsável por salvar a demanda
         * @param registro
         */
        scope.persistir = function (registro) {
            $scope.formRegistro.$setSubmitted();
            if ($scope.formRegistro.$valid) {
                var demanda = angular.copy(registro);
                demanda.cpfUsuario = $rootScope.usuarioLogado.cpf;
                demanda.nomeUsuario = $rootScope.usuarioLogado.nome;

                demanda.endereco.idMunicipio = demanda.endereco.municipio.id;
                demanda.endereco.nomeMunicipio = demanda.endereco.municipio.nome;

                if (scope.ripPesquisado === true) {
                    demanda.endereco.rip = '';
                }

                if (!demanda.endereco.latitude || !demanda.endereco.longitude) {
                    //TODO [jbjares] descomentar codigo abaixo quando o problema relacionado ao servico de geolocation for resolvido.
                    //ComumFactory.mostrarMensagemError('Selecione uma localização');
                    //demanda.endereco.latitude = -5350420.892414739;
                    //demanda.endereco.longitude = -1794597.1077059904;
                    //
                    return;
                }

                delete demanda.endereco.municipio;

                if (demanda.id) {
                    service.update(demanda).then(function () {
                        $state.go('fiscalizacao.demanda.listar');
                    });
                } else {
                    service.create(demanda).then(function () {
                        $state.go('fiscalizacao.demanda.listar');
                    });
                }

            } else {
                if ($scope.formRegistro.$error.cnpj || $scope.formRegistro.$error.cpf) {
                    ComumFactory.mostrarMensagemError('CPF/CNPJ Inválido!');
                } else {
                    ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
                }
            }
        };

        /**
         * Obtém uma lista de motivações
         * @param callback
         */
        scope.getMotivacoes = function (callback) {
            scope._motivacoes = {};
            service.getMotivacoes().then(function (objectReturn) {
                scope._motivacoes = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de assuntos
         * @param callback
         */
        scope.getAssuntos = function (callback) {
            scope._assuntos = {};
            service.getAssuntos().then(function (objectReturn) {
                scope._assuntos = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de tipos de imóvel
         * @param callback
         */
        scope.getTiposImovel = function (callback) {
            scope._tiposImovel = {};
            service.getTiposImovel().then(function (objectReturn) {
                scope._tiposImovel = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de zoneamentos
         * @param callback
         */
        scope.getZoneamentos = function (callback) {
            scope._zoneamentos = {};
            service.getZoneamentos().then(function (objectReturn) {
                scope._zoneamentos = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        scope.getEstados = function (callback) {
            scope._estados = {};
            integradorService.getEstados().then(function (objectReturn) {
                scope._estados = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        /**
         * obtém uma lista de municipios
         * @param sigla
         * @param callback
         */
        scope.getMunicipios = function (sigla, callback) {
            if (sigla) {
                scope._municipios = [];
                integradorService.getMunicipios(sigla).then(function (objectReturn) {
                    scope._municipios = objectReturn.data;
                    if (callback) {
                        callback();
                    }
                });
            }
        };

		scope.onChangeMunicipio = function(estado, municipio) {
			console.log('scope.onChangeMunicipio', estado, municipio)
			CartografiaServiceFiscalizacao.buscarGeometria({uf: estado, municipio: municipio.nome}).then(function (result) {
				if (result.data) {
					NotificadorServiceFiscalizacao.notify($scope, result.data, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.CENTRALIZAR_POR_MUNICIPIO);
				}
			});
		}


        /**
         * Obtém o endereço através do cep informado pelo usuário
         * @param cep
         */
        scope.getCEP = function (cep) {
            if (cep && cep.length === 8) {
                scope._endereco = {};
                scope.buscarCEP = true;
                integradorService.getCEP(cep).then(function (objectReturn) {
                    scope.buscarCEP = false;
                    if (!objectReturn.data) {
                        ComumFactory.mostrarMensagemError('CEP não encontrado!');
                        scope.registro.endereco.cep = '';
                    }

                    if (getJurisdicao().indexOf(objectReturn.data.uf) === -1 ) {
                        iniciaEndereco();
                        ComumFactory.mostrarMensagemError('O CEP informado não pertence a sua Unidade Federativa!');
                        return;
                    }

                    var retorno = objectReturn.data;
                    scope.registro.endereco.logradouro = retorno.logradouro;
                    scope.registro.endereco.bairro = retorno.bairro;

                    for (var obj in scope._tipoLogradouro) {
                        if (scope._tipoLogradouro[obj] == retorno.tipoLogradouro) {
                            scope.registro.endereco.tipoLogradouro = obj;
                        }
                    }

                    for (var i = 0; i < scope._usuario.jurisdicoes.length; i++) {
                        if (scope._usuario.jurisdicoes[i] === retorno.uf) {
                            scope.registro.endereco.estado = scope._usuario.jurisdicoes[i];
                            scope.getMunicipios((scope.registro.endereco.estado), function() {
                                for (var i = 0; i < scope._municipios.length; i++) {
                                    if (scope._municipios[i].codigo == retorno.codMunicipio) {
                                        scope.registro.endereco.municipio = scope._municipios[i];
                                        break;
                                    }
                                }
                            });
                            break;
                        }
                    }

                    CartografiaServiceFiscalizacao.buscarGeometria(objectReturn.data).then(function (result) {
                        if (result.data) {
                            NotificadorServiceFiscalizacao.notify($scope, result.data, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.CENTRALIZAR_POR_MUNICIPIO);
                        }
                    });

                    scope.validaEstadoSelecionado();
                }, function () {
                    scope.buscarCEP = false;
                    ComumFactory.mostrarMensagemError('CEP não encontrado!');
                    scope.registro.endereco.cep = '';
                });
            }
        };

        scope.validaEstadoSelecionado = function(){
          if(scope.registro.endereco.estado !== scope.ufPermitida && scope.demandaOfExistente){
              scope.registro.endereco = {};
              ComumFactory.mostrarMensagemError('Favor adicionar apenas demandas da mesma UF');
          }
        };

        scope.getRIP = function (rip) {
            if (rip && rip.length === 8) {
                scope._endereco = {};
                integradorService.getRIP(rip).then(function (objectReturn) {

                    if (!objectReturn.data) {
                        iniciaEndereco();
                        ComumFactory.mostrarMensagemError('RIP não encontrado!');
                        return;
                    }
                    if (getJurisdicao().indexOf(objectReturn.data.enderecamento.uf) === -1 ) {
                        iniciaEndereco();
                        ComumFactory.mostrarMensagemError('O RIP informado não está circunscrito na sua Unidade Federativa!');
                        return;
                    }
                    scope.ripValido = true;
                    scope.mapaCarregado = false;
                    var retorno = objectReturn.data;
                    var endereco = retorno.enderecamento;

                    for (var obj in scope._tipoLogradouro) {
                        if (scope._tipoLogradouro[obj] === endereco.tipoLogradouro) {
                            scope.registro.endereco.tipoLogradouro = obj;
                        }
                    }

                    scope.registro.endereco.logradouro = endereco.logradouro;
                    scope.registro.endereco.cep = endereco.cep;
                    scope.registro.endereco.bairro = endereco.bairro;
                    scope.registro.areaImovel = retorno.terreno.areaTotalCadastral;

                    var imovel = {};
                    imovel.coordinates = retorno.pontoImovel.coordinates;
                    imovel.type = 'Point';

                    scope.registro.endereco.latitude = retorno.pontoImovel.coordinates[0];
                    scope.registro.endereco.longitude = retorno.pontoImovel.coordinates[1];

                    scope.validaPonto(imovel);


                    NotificadorServiceFiscalizacao.notify(scope, imovel.coordinates, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.ATUALIZAR_COORDENADAS_DO_PONTO);
                    NotificadorServiceFiscalizacao.notify(scope, imovel, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.MARCAR_PONTO_POR_COORDENADAS);

                    scope.getEstados(function () {
                        for (var i = 0; i < scope._estados.length; i++) {
                            if (scope._estados[i].sigla === endereco.uf) {
                                scope.registro.endereco.estado = scope._estados[i].sigla;
                                scope.getMunicipios(endereco.uf, function () {
                                    for (var i = 0; i < scope._municipios.length; i++) {
                                        if (scope._municipios[i].nome === endereco.municipio) {
                                            scope.registro.endereco.municipio = scope._municipios[i];
                                            break;
                                        }
                                    }
                                });
                            }
                        }
                    });

                    scope.dadosEnderecoValida();
                }, function () {
                    iniciaEndereco();
                    ComumFactory.mostrarMensagemError('RIP não encontrado!');
                });
            } else {
                iniciaEndereco();
                ComumFactory.mostrarMensagemError('Informe um RIP válido!');
            }
        };

        scope.getEdesc = function (callback) {
            scope._nucleoEdesc = {};
            if (scope.registro.isEdesc) {
                service.getEdesc().then(function (objectReturn) {
                    scope._nucleoEdesc = objectReturn.data;
                    if (callback) {
                        callback();
                    }
                });
            }
        };

        /**
         * Obtém o registro de histórico da demanda
         */
        scope.getHistorico = function () {
            scope._historico = {};
            service.getHistorico(scope.registro.id).then(function (objectReturn) {
                scope._historico = objectReturn.data;
            });
        };

        /**
         * obtém uma lista de status
         * @param callback
         */
        scope.getStatus = function (callback) {
            service.getStatus().then(function (objectReturn) {
                scope._status = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém o registro do usuário logado
         * @param callback
         */
        scope.getUsuario = function (callback) {
            scope._usuario = {};
            service.getUsuario().then(function (objectReturn) {
                scope._usuario = objectReturn.data;
                if (callback) {
                    callback();
                }
            });
        };

        /**
         * Obtém uma lista de tipos de logradouro
         * @param callback
         */
        scope.getTipoLogradouro = function (callback) {
            scope._tipoLogradouro = {};
            service.getTipoLogradouro().then(function (objectReturn) {
                scope._tipoLogradouro = objectReturn.data;

                if (callback) {
                    callback();
                }
            });
        };

        scope.changeProcesso = function () {
            scope.registro.processoAdministrativo = null;
        };

        scope.naoPossuiRip = function () {
            scope.possuiRip = false;
            scope.registro.rip = null;
        };

        scope.changeAssunto = function (assunto) {
            if (assunto && assunto.tipo !== 'OUTROS') {
                scope.registro.outros = null;
            }
        };

        scope.changePaf = function () {
            scope.registro.assunto = null;
        };

        scope.changePrazo = function () {
            scope.registro.dtPrazo = '';
        };

        /**
         * Prepara a visualizçaão da modal de detalhes do histórico da demanda
         * @param ev
         * @param historico
         */
        scope.showModalDetalheHistorico = function (ev, historico) {
            scope._detalhes = historico.detalhes;
            $mdDialog.show({
                controller: function () {
                    return scope;
                },
                controllerAs: 'scope',
                templateUrl: 'scripts-fiscalizacao/pages/demanda/detalhar/views/demanda-historico-modal.tmpl.html',
                parent: angular.element(document.body),
                targetEvent: ev,
                autoWrap: false,
                disableParentScroll: false,
                clickOutsideToClose: true,
                fullscreen: true,
                onComplete: function () {
                    scope.scrollToTop();
                }
            });
        };


        /**
         * Prepara a visualizçaão da modal de justificativa do histórico da demanda
         * @param ev
         * @param historico
         */
        scope.showModalHistoricoJustificativaDemanda = function (ev, historico) {
            scope._justificativaDemanda = historico.justificativaDemanda;
            scope._historicoJustificativa = historico;
            service.getRelatorioOrdemFiscalizacaoByDemandaId(historico.demanda.id).then(function (objectReturn) {
                scope.relatorioFiscalizacao = objectReturn.data;

                $mdDialog.show({
                    controller: function () {
                        return scope;
                    },
                    controllerAs: 'scope',
                    templateUrl: 'scripts-fiscalizacao/pages/demanda/detalhar/views/demanda-historico-justificativa-modal.tmpl.html',
                    parent: angular.element(document.body),
                    targetEvent: ev,
                    autoWrap: false,
                    disableParentScroll: false,
                    clickOutsideToClose: true,
                    fullscreen: true,
                    onComplete: function () {
                        scope.scrollToTop();
                    }
                });
            });
            
        };


        /**
         * Função responsável para dar um scroll automático para o topo da tela ao abrir uma modal
         */
        scope.scrollToTop = function () {
            var elmnt = document.getElementById('modal');
            elmnt.scrollTop = 0;
        };

        /**
         * Função responsável por fechar uma modal
         */
        scope.cancelModal = function () {
            $mdDialog.cancel();
        };

        /**
         * Exporta um arquivo de extensão KML através de uma ação no mapa
         * @param kml
         */
        scope.exportarKml = function (kml) {
            var element = document.createElement('a');
            element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(kml));
            element.setAttribute('download', 'mapa.kml');

            element.style.display = 'none';
            document.body.appendChild(element);

            element.click();

            document.body.removeChild(element);
        };

        scope.suspender = function () {
            var confirm = $mdDialog.confirm()
                .title('Atenção!')
                .textContent('Tem certeza que deseja suspender esta demanda?')
                .ok('Confirmar')
                .cancel('Cancelar');
            $mdDialog.show(confirm).then(function () {
                service.suspender(scope.registro.id).then(function () {
                    ComumFactory.mostrarMensagemSucesso('Demanda foi suspendida com sucesso!');
                    $state.go('fiscalizacao.demanda.listar');
                });
            });
        };

        scope.restaurar = function () {
            var confirm = $mdDialog.confirm()
                .title('Atenção!')
                .textContent('Tem certeza que deseja restaurar esta demanda?')
                .ok('Confirmar')
                .cancel('Cancelar');

            $mdDialog.show(confirm).then(function () {
                service.restaurar(scope.registro.id).then(function () {
                    ComumFactory.mostrarMensagemSucesso('Demanda foi restaurada com sucesso!');
                    $state.go('fiscalizacao.demanda.listar');
                });
            });
        };

        /**
         * Obtém uma lista de jurisdições do usuário
         * @returns {*}
         */
        function getJurisdicao() {
            if (!scope._usuario.jurisdicoes) {
                return ['DF'];
            } else {
                return scope._usuario.jurisdicoes;
            }
        }

        scope.dadosEnderecoValida = function () {
            if (scope.ripPesquisado) {
                scope.dadosEndereco = true;
                iniciaEndereco();
                scope.registro.areaImovel = '';
                scope.ripValido = false;
                scope.pontoValidado = false;
                scope.registro.endereco.codigoSistemaReferencia = '4674';
                scope.registro.endereco.formatoCoordenadas = 'GMS';
                return;
            } else if (scope.registro.endereco.rip && scope.ripValido) {
                scope.dadosEndereco = true;
                return;
            }
            scope.dadosEndereco = false;
        };

        /**
         * Valida se o ponto marcado no mapa pertence a jurisdição do usuário
         * @param imovel
         */
        scope.validaPonto = function (imovel) {
            scope.pontoValidado = false;
            integradorService.validar(imovel).then(function (objectResponse) {
                if (objectResponse.data.noPais === 'Brasil' && getJurisdicao().indexOf(objectResponse.data.sgUf) > -1) {

					if(	scope.registro.endereco.estado && (objectResponse.data.sgUf !== scope.registro.endereco.estado)){
                        //TODO [jbjares] descomentar codigo abaixo quando o problema relacionado ao servico de geolocation for resolvido.
                        removerPonto();
						ComumFactory.mostrarMensagemError('O ponto selecionado não corresponde a UF selecionada!');
						return;
					}

                    if (objectResponse.data.noNaturezaImovel) {
                        scope.getZoneamentos(function () {
                            for (var i = 0; i < scope._zoneamentos.length; i++) {
                                if (scope._zoneamentos[i].nome === objectResponse.data.noNaturezaImovel) {
                                    scope.registro.endereco.zoneamento = scope._zoneamentos[i];
                                    scope.pontoValidado = true;
                                    break;
                                }
                            }
						});

						scope.pontoInfo = objectResponse.data;
                        NotificadorServiceFiscalizacao.notify(scope, imovel, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.MARCAR_PONTO_POR_COORDENADAS);
                    }
                } else {
                    removerPonto();
                    ComumFactory.mostrarMensagemError('O ponto selecionado não corresponde a sua UF!');
                }
            }).catch(function(){
                //TODO [jbjares] descomentar codigo abaixo quando o problema relacionado ao servico de geolocation for resolvido.
				removerPonto();
				ComumFactory.mostrarMensagemError('Não foi possível validar o ponto. O sistema Geo serviço está fora do ar.');
			});

		};

		function removerPonto() {
			$timeout(function () {
				var endereco = {};
				endereco.coordinates = [];
				endereco.coordinates[0] = null;
				endereco.coordinates[1] = null;

				var object = null;

				NotificadorServiceFiscalizacao.notify(object, endereco.coordinates, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.ATUALIZAR_COORDENADAS_DO_PONTO);
				NotificadorServiceFiscalizacao.notify(object, endereco, MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES.MARCAR_PONTO_POR_COORDENADAS);

				scope.registro.endereco.codigoSistemaReferencia = '';
				scope.registro.endereco.estado = '';
				scope.registro.endereco.formatoCoordenadas = '';
				scope.registro.endereco.latitude = null;
				scope.registro.endereco.longitude = null;

			});
		}

        /**
         * Prepara o registro de endereço
         * @param callback
         */
        function iniciaEndereco(callback) {
            scope.registro.endereco = {};

            if(callback){
                callback();
            }
        }

        /**
         * limpa o número RIP do endereço
         * @param rip
         */
        scope.limpaRip = function (rip) {
            if (!rip || rip.length < 8) {
                scope.registro.endereco.rip = '';
            }
        };

        /**
         * Obtém o registro de ordem de fiscalização através de seu identificador único (ID),
         * e redireciona o usuário para a tela de consulta de ordem de fiscalização
         * @param demandaId
         */
        scope.getOrdemFiscalizacaoByDemandaId = function (demandaId) {
            service.getOrdemFiscalizacaoByDemandaId(demandaId).then(function (objectReturn) {
                scope.ordemFiscalizacao = objectReturn.data;

                var url = $state.href('fiscalizacao.ordemFiscalizacao.alterar', {id: scope.ordemFiscalizacao.id});
                window.open(url, '_blank');
            });
        };

        /**
         * Obtém o registro de relatório de ordem de fiscalização através de seu identificador único (ID),
         * e redireciona o usuário para a tela de consultar relatório de fiscalização
         * @param demandaId
         */
        scope.getRelatorioOrdemFiscalizacaoByDemandaId = function (demandaId) {
            service.getRelatorioOrdemFiscalizacaoByDemandaId(demandaId).then(function (objectReturn) {
                scope.relatorioFiscalizacao = objectReturn.data;

                var url = $state.href('fiscalizacao.relatorio.consultar', {id: scope.relatorioFiscalizacao.id});
                window.open(url, '_blank');
            });
        };

        /**
         * Obtém o registro de ordem de fiscalização ou relatório de ordem de fiscalização,
         * dependendo so status da demanda
         * @param ocorrencia
         * @param demandaId
         */
        scope.verificaOcorrenciaHistorico = function (ocorrencia, demandaId) {
            if (ocorrencia.substring(0, 17) === 'Demanda executada') {
                scope.getRelatorioOrdemFiscalizacaoByDemandaId(demandaId);
                scope.link = true;
            } else {
                if (ocorrencia.substring(0, 17) === 'Demanda vinculada') {
                    scope.getOrdemFiscalizacaoByDemandaId(demandaId);
                    scope.link = true;
                }
            }
        };

        
        /**
         * Obtém o registro de relatório de ordem de fiscalização através de seu identificador único (ID),
         *
         * @param demandaId
         */
        scope.getRelatorioOrdemFiscalizacaoByDemandaIdSemRedirecionamento = function (demandaId) {
            
            service.getRelatorioOrdemFiscalizacaoByDemandaId(demandaId).then(function (objectReturn) {
                scope.relatorioFiscalizacao = objectReturn.data;

            });
        };

        scope.init();
    }
})();

(function () {
  'use strict';
  var moduleName = "ngMaterialDatePicker";

  var VIEW_STATES = {
    DATE: 0,
    HOUR: 1,
    MINUTE: 2
  };

  var css = function (el, name) {
    el = angular.element(el);
    return ('getComputedStyle' in window) ? window.getComputedStyle(el[0])[name] : el.css(name);
  };

  var template = '<md-dialog class="dtp" layout="column" style="width: 300px;">'
    + '    <md-dialog-content class="dtp-content">'
    + '        <div class="dtp-date-view">'
    + '            <header class="dtp-header">'
    + '                <div class="dtp-actual-day" ng-show="picker.dateMode">{{picker.currentDate.format("dddd")}}</div>'
    + '                <div class="dtp-actual-day" ng-show="picker.timeMode">{{picker.params.shortTime ? picker.currentDate.format("A") : " "}}</div>'
    + '                <div class="dtp-close text-right">'
    + '                    <a href="#" mdc-dtp-noclick ng-click="picker.hide()">&times;</a>'
    + '                </div>'
    + '            </header>'
    + '            <div class="dtp-date" ng-show="picker.params.date">'
    + '                <div layout="column">'
    + '                    <div class="dtp-actual-month">{{picker.currentDate.format("MMM") | uppercase}}</div>'
    + '                </div>'
    + '                <div class="dtp-actual-num">{{picker.currentDate.format("DD")}}</div>'
    + '                <div layout="row">'
    + ' <div ng-click="picker.incrementYear(-1)" class="dtp-year-btn dtp-year-btn-prev" flex="30"><span ng-if="picker.isPreviousYearVisible()" >&#x25B2;</span></div>'
    + '                    <div class="dtp-actual-year" flex>{{picker.currentDate.format("YYYY")}}</div>'
    + ' <div ng-click="picker.incrementYear(1)" class="dtp-year-btn dtp-year-btn-next" flex="30"><span ng-if="picker.isNextYearVisible()" >&#x25BC;</span></div>'
    + '                </div>'
    + '            </div>'//start time
    + '            <div class="dtp-time" ng-show="picker.params.time && !picker.params.date">'
    + '                <div class="dtp-actual-maxtime">{{picker.currentNearest5Minute().format(picker.params.shortTime ? "hh:mm" : "HH:mm")}}</div>'
    + '            </div>'
    + '            <div class="dtp-picker">'
    + '                <mdc-datetime-picker-calendar date="picker.currentDate" picker="picker" class="dtp-picker-calendar" ng-show="picker.currentView === picker.VIEWS.DATE"></mdc-datetime-picker-calendar>'
    + '                <div class="dtp-picker-datetime" ng-cloak ng-if="picker.currentView !== picker.VIEWS.DATE">'
    + '                    <div class="dtp-actual-meridien">'
    + '                        <div class="left p20">'
    + '                            <a href="#" mdc-dtp-noclick class="dtp-meridien-am" ng-class="{selected: picker.meridien == \'AM\'}" ng-click="picker.selectAM()">{{picker.params.amText}}</a>'
    + '                        </div>'
    + '                        <div ng-show="!picker.timeMode" class="dtp-actual-time p60">{{picker.currentNearest5Minute().format(picker.params.shortTime ? "hh:mm" : "HH:mm")}}</div>'
    + '                        <div class="right p20">'
    + '                            <a href="#" mdc-dtp-noclick class="dtp-meridien-pm" ng-class="{selected: picker.meridien == \'PM\'}" ng-click="picker.selectPM()">{{picker.params.pmText}}</a>'
    + '                        </div>'
    + '                        <div class="clearfix"></div>'
    + '                    </div>'
    + '                    <mdc-datetime-picker-clock mode="hours" ng-if="picker.currentView === picker.VIEWS.HOUR"></mdc-datetime-picker-clock>'
    + '                    <mdc-datetime-picker-clock mode="minutes" ng-if="picker.currentView === picker.VIEWS.MINUTE"></mdc-datetime-picker-clock>'
    + '                </div>'
    + '            </div>'
    + '        </div>'
    + '    </md-dialog-content>'
    + '    <md-dialog-actions class="dtp-buttons">'
    + '            <md-button class="dtp-btn-ok md-button" ng-click="picker.today()"> {{picker.params.todayText}}</md-button>'
    + '            <md-button class="dtp-btn-cancel md-button" ng-click="picker.cancel()"> {{picker.params.cancelText}}</md-button>'
    + '            <md-button class="dtp-btn-ok md-button" ng-click="picker.ok()"> {{picker.params.okText}}</md-button>'
    + '      </md-dialog-actions>'
    + '</md-dialog>';


  angular.module(moduleName, ['ngMaterial'])
    .provider('mdcDatetimePickerDefaultLocale', function () {
      this.locale = window.navigator.userLanguage || window.navigator.language || 'pt';

      this.$get = function () {
        return this;
      };

      this.setDefaultLocale = function (localeString) {
        this.locale = localeString;
      };
    })
    .factory('mdcDefaultParams', ['mdcDatetimePickerDefaultLocale', function (mdcDatetimePickerDefaultLocale) {
      var default_params = {
        date: true,
        time: true,
        format: 'DD-MM-YYYY',
        minDate: null,
        maxDate: null,
        currentDate: null,
        lang: mdcDatetimePickerDefaultLocale.locale,
        weekStart: 0,
        shortTime: false,
        cancelText: 'Cancelar',
        okText: 'OK',
        amText: 'AM',
        pmText: 'PM',
        todayText: 'Hoje',
        disableDates: []
      };
      return default_params;
    }])
    .directive('mdcDatetimePicker', ['$mdDialog', '$timeout',
      function ($mdDialog, $timeout) {

        return {
          restrict: 'A',
          require: 'ngModel',
          scope: {
            currentDate: '=ngModel',
            ngChange: '&',
            time: '=',
            date: '=',
            minDate: '=',
            maxDate: '=',
            disableDates: '=',
            shortTime: '=',
            weekStart: '=',
            format: '@',
            cancelText: '@',
            okText: '@',
            lang: '@',
            amText: '@',
            pmText: '@',
            showTodaysDate: '@',
            todayText: '@'
          },
          link: function (scope, element, attrs, ngModel) {
            var isOn = false;
            if (!scope.format) {
              if (scope.date && scope.time) {
                scope.format = 'DD/MM/YYYY HH:mm';
              } else if (scope.date) {
                scope.format = 'DD/MM/YYYY';
              } else {
                scope.format = 'HH:mm';
              }
            }

            var dateOfTheDay = null;
            if (scope.showTodaysDate !== undefined && scope.showTodaysDate !== "false") {
              dateOfTheDay = moment();
            }

            if (angular.isString(scope.currentDate) && scope.currentDate !== '') {
              scope.currentDate = moment(scope.currentDate, scope.format);
            }

            if (ngModel) {
              ngModel.$formatters.push(function (value) {
                if (typeof value === 'undefined') {
                  return;
                }
                var m = moment(value);
                return m.isValid() ? m.format(scope.format) : '';
              });
            }

            element.attr('readonly', '');
            //@TODO custom event to trigger input
            element.on('focus', function (e) {
              e.preventDefault();
              element.blur();
              if (isOn) {
                return;
              }
              isOn = true;
              var options = {};
              for (var i in attrs) {
                if (scope.hasOwnProperty(i) && !angular.isUndefined(scope[i])) {
                  options[i] = scope[i];
                }
              }
              options.currentDate = scope.currentDate;
              options.showTodaysDate = dateOfTheDay;

              var locals = {options: options};
              $mdDialog.show({
                template: template,
                controller: PluginController,
                controllerAs: 'picker',
                locals: locals,
                openFrom: element,
                parent: angular.element(document.body),
                bindToController: true,
                disableParentScroll: false,
                hasBackDrop: false,
                skipHide: true
              })
                .then(function (v) {
                  scope.currentDate = v ? v._d : v;
                  isOn = false;

                  if (!moment(scope.currentDate).isSame(options.currentDate)) {
                    $timeout(scope.ngChange, 0);
                  }

                  element.blur()

                }, function () {
                  isOn = false;
                  element.blur()
                })
              ;
            });
          }
        };
      }])
    /** Returns a service that opens a dialog when the attribute shown is called
     The dialog serves to select a date/time/etc. depending on the options given to the function show

     @param options extends mdcDefaultParams
     {
       date: {boolean} =true,
         time: {boolean] =true,
         format: {string} ='YYYY-MM-DD',
         minDate: {strign} =null,
         maxDate: {string} =null,
         currentDate: {string} =null,
         lang: {string} =mdcDatetimePickerDefaultLocale,
         weekStart: {int} =0,
         shortTime: {boolean} =false,
         cancelText: {string} ='Cancel',
         todayText: {string} ='Today',
         okText: {string} ='OK',
         amText: {string} ='AM',
         pmText: {string} ='PM'
     }
   @return promise
    */
    .factory('mdcDateTimeDialog', ["$mdDialog", "$q", "mdcDefaultParams", function ($mdDialog, $q, mdcDefaultParams) {
      var accepted_options = Object.keys(mdcDefaultParams);
      var service = {
        show: function (options) {
          var deferred = $q.defer();
          var params = angular.copy(mdcDefaultParams);
          for (var i in options) {
            if (accepted_options.indexOf[i] != -1 && options.hasOwnProperty(i)) {
              params = options[i];
            }
          }
          var locals = {options: options};
          $mdDialog.show({
            template: template,
            controller: PluginController,
            controllerAs: 'picker',
            locals: locals,
            parent: angular.element(document.body),
            bindToController: true,
            clickOutsideToClose: true,
            disableParentScroll: false,
            skipHide: true
          })
            .then(function (v) {
              var currentDate = v ? v._d : v;
              deferred.resolve(v ? v._d : v);
            }, function () {
              deferred.reject();
            });
          return deferred.promise;
        }
      };

      return service;
    }])
  ;

  var PluginController = function ($scope, $mdDialog, mdcDefaultParams) {
    this.currentView = VIEW_STATES.DATE;
    this._dialog = $mdDialog;

    this._attachedEvents = [];
    this.VIEWS = VIEW_STATES;
    this.params = mdcDefaultParams;
    this.meridien = 'AM';
    this.params = angular.extend(this.params, this.options);

    this.init();
  };

  PluginController.$inject = ['$scope', '$mdDialog', 'mdcDefaultParams'];
  PluginController.prototype = {
    init: function () {
      this.timeMode = this.params.time && !this.params.date;
      this.dateMode = this.params.date;
      this.initDates();
      this.start();
    },
    currentNearest5Minute: function () {
      var date = this.currentDate || moment();
      var minutes = (5 * Math.round(date.minute() / 5));
      if (minutes >= 60) {
        minutes = 55; //always push down
      }
      return moment(date).minutes(minutes);
    },
    initDates: function () {
      var that = this;
      var _dateParam = function (input, fallback) {
        var ret = null;
        if (angular.isDefined(input) && input !== null && input !== '') {
          if (angular.isString(input)) {
            if (typeof(that.params.format) !== 'undefined' && that.params.format !== null) {
              ret = moment(input, that.params.format).locale(that.params.lang);
            }
            else {
              ret = moment(input).locale(that.params.lang);
            }
          } else if (typeof input === 'number') {
            ret = moment(input).locale(that.params.lang);
          } else {
            if (angular.isDate(input)) {
              var x = input.getTime();
              ret = moment(x, "x").locale(that.params.lang);
            } else if (input._isAMomentObject) {
              ret = input;
            }
          }
        }
        else {
          ret = fallback;
        }
        return ret;
      };

      this.currentDate = _dateParam(this.params.currentDate, moment());
      this.minDate = _dateParam(this.params.minDate);
      this.maxDate = _dateParam(this.params.maxDate);
      this.disableDates = this.params.disableDates.map(function (x) {
        return moment(x).format('MMMM Do YYYY')
      });
      this.selectDate(this.currentDate);
    },
    initDate: function () {
      this.currentView = VIEW_STATES.DATE;
    },
    initHours: function () {
      this.currentView = VIEW_STATES.HOUR;
    },
    initMinutes: function () {
      this.currentView = VIEW_STATES.MINUTE;
    },
    isAfterMinDate: function (date, checkHour, checkMinute) {
      var _return = true;

      if (typeof(this.minDate) !== 'undefined' && this.minDate !== null) {
        var _minDate = moment(this.minDate);
        var _date = moment(date);

        if (!checkHour && !checkMinute) {
          _minDate.hour(0);
          _minDate.minute(0);

          _date.hour(0);
          _date.minute(0);
        }

        _minDate.second(0);
        _date.second(0);
        _minDate.millisecond(0);
        _date.millisecond(0);

        if (!checkMinute) {
          _date.minute(0);
          _minDate.minute(0);

          _return = (parseInt(_date.format("X")) >= parseInt(_minDate.format("X")));
        }
        else {
          _return = (parseInt(_date.format("X")) >= parseInt(_minDate.format("X")));
        }
      }

      return _return;
    },
    isBeforeMaxDate: function (date, checkTime, checkMinute) {
      var _return = true;

      if (typeof(this.maxDate) !== 'undefined' && this.maxDate !== null) {
        var _maxDate = moment(this.maxDate);
        var _date = moment(date);

        if (!checkTime && !checkMinute) {
          _maxDate.hour(0);
          _maxDate.minute(0);

          _date.hour(0);
          _date.minute(0);
        }

        _maxDate.second(0);
        _date.second(0);
        _maxDate.millisecond(0);
        _date.millisecond(0);

        if (!checkMinute) {
          _date.minute(0);
          _maxDate.minute(0);

          _return = (parseInt(_date.format("X")) <= parseInt(_maxDate.format("X")));
        }
        else {
          _return = (parseInt(_date.format("X")) <= parseInt(_maxDate.format("X")));
        }
      }

      return _return;
    },
    isInDisableDates: function (date) {
      var dut = date.format('MMMM Do YYYY')
      if (this.disableDates.indexOf(dut) > -1) {
        return false;
      }
      return true;
    },
    selectDate: function (date) {
      if (date) {
        this.currentDate = moment(date);
        if (!this.isAfterMinDate(this.currentDate)) {
          this.currentDate = moment(this.minDate);
        }

        if (!this.isBeforeMaxDate(this.currentDate)) {
          this.currentDate = moment(this.maxDate);
        }
        this.currentDate.locale(this.params.lang);
        this.calendarStart = moment(this.currentDate);
        this.meridien = this.currentDate.hour() >= 12 ? 'PM' : 'AM';
      }
    },
    setName: function () {
      var text = "";
      var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

      for (var i = 0; i < 5; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
      }

      return text;
    },
    isPM: function () {
      return this.meridien === 'PM';
    },
    incrementYear: function (amount) {
      if (amount === 1 && this.isNextYearVisible()) {
        this.selectDate(this.currentDate.add(amount, 'year'));
      }

      if (amount === -1 && this.isPreviousYearVisible()) {
        this.selectDate(this.currentDate.add(amount, 'year'));
      }
    },
    isPreviousMonthVisible: function () {
      return this.calendarStart && this.isAfterMinDate(moment(this.calendarStart).startOf('month'), false, false);
    },
    isNextMonthVisible: function () {
      return this.calendarStart && this.isBeforeMaxDate(moment(this.calendarStart).endOf('month'), false, false);
    },
    isPreviousYearVisible: function () {
      return this.calendarStart && this.isAfterMinDate(moment(this.calendarStart).startOf('year'), false, false);
    },
    isNextYearVisible: function () {
      return this.calendarStart && this.isBeforeMaxDate(moment(this.calendarStart).endOf('year'), false, false);
    },
    isHourAvailable: function (hour) {
      var _date = moment(this.currentDate);
      _date.hour(this.convertHours(hour)).minute(0).second(0);
      return this.isAfterMinDate(_date, true, false) && this.isBeforeMaxDate(_date, true, false);
    },
    isMinuteAvailable: function (minute) {
      var _date = moment(this.currentDate);
      _date.minute(minute).second(0);
      return this.isAfterMinDate(_date, true, true) && this.isBeforeMaxDate(_date, true, true);
    },
    start: function () {
      this.currentView = VIEW_STATES.DATE;
      //this.initDates();
      if (this.params.date) {
        this.initDate();
      } else {
        if (this.params.time) {
          this.initHours();
        }
      }
    },
    today: function () {
      this.selectDate(Date.now());
    },
    ok: function () {
      switch (this.currentView) {
        case VIEW_STATES.DATE:
          if (this.params.time === true) {
            this.initHours();
          }
          else {
            this.hide(true);
          }
          break;
        case VIEW_STATES.HOUR:
          this.initMinutes();
          break;
        case VIEW_STATES.MINUTE:
          this.hide(true);
          break;
      }
    },
    cancel: function () {
      if (this.params.time) {
        switch (this.currentView) {
          case VIEW_STATES.DATE:
            this.hide();
            break;
          case VIEW_STATES.HOUR:
            if (this.params.date) {
              this.initDate();
            }
            else {
              this.hide();
            }
            break;
          case VIEW_STATES.MINUTE:
            this.initHours();
            break;
        }
      }
      else {
        this.hide();
      }
    },
    selectMonthBefore: function () {
      this.calendarStart.subtract(1, 'months');
    },
    selectMonthAfter: function () {
      this.calendarStart.add(1, 'months');
    },
    selectYearBefore: function () {
      this.calendarStart.subtract(1, 'years');
    },
    selectYearAfter: function () {
      this.calendarStart.add(1, 'years');
    },
    selectAM: function () {
      if (this.isHourAvailable(0) || this.isHourAvailable(12)) {
        if (this.currentDate.hour() >= 12) {
          this.selectDate(this.currentDate.subtract(12, 'hours'));
        }
        if (!this.isHourAvailable(this.currentDate.hour())) {
          this.selectDate(this.currentDate.hour(this.minDate.hour()));
        }
        if (!this.isMinuteAvailable(this.currentDate.minute())) {
          this.selectDate(this.currentDate.minute(this.minDate.minute()));
        }
      }
    },
    selectPM: function () {
      if (this.isHourAvailable(13) || this.isHourAvailable(24)) {
        if (this.currentDate.hour() < 12) {
          this.selectDate(this.currentDate.add(12, 'hours'));
        }
        if (!this.isHourAvailable(this.currentDate.hour())) {
          this.selectDate(this.currentDate.hour(this.maxDate.hour()));
        }
        if (!this.isMinuteAvailable(this.currentDate.minute())) {
          this.selectDate(this.currentDate.minute(this.maxDate.minute()));
        }
      }
    },
    convertHours: function (h) {
      var _return = h;
      if ((h < 12) && this.isPM())
        _return += 12;

      return _return;
    },
    hide: function (okBtn) {
      if (okBtn) {
        this._dialog.hide(this.currentDate);
      } else {
        this._dialog.cancel();
      }
    }
  };


  angular.module(moduleName)
    .directive('mdcDatetimePickerCalendar', [
      function () {

        var YEAR_MIN = 1920,
          YEAR_MAX = new Date().getFullYear() + 30,
          MONTHS_IN_ALL = (YEAR_MAX - YEAR_MIN + 1) * 12,
          ITEM_HEIGHT = 240,
          MONTHS = [];
        for (var i = 0; i < MONTHS_IN_ALL; i++) {
          MONTHS.push(i);
        }

        var currentMonthIndex = function (date, low) {
          low = low ? low : 0;
          var year = date.year();
          var month = date.month();
          return (((year - YEAR_MIN) * 12) + month - 1 ) - low;
        };

        return {
          restrict: 'E',
          scope: {
            picker: '=',
            date: '='
          },
          bindToController: true,
          controllerAs: 'cal',
          controller: ['$scope', function ($scope) {
            this.$onInit = function () {
              var calendar = this,
                picker = this.picker,
                days = [];

              for (var i = picker.params.weekStart; days.length < 7; i++) {
                if (i > 6) {
                  i = 0;
                }
                days.push(i.toString());
              }

              calendar.week = days;
              if (!picker.maxDate && !picker.minDate) {
                calendar.months = MONTHS;
              } else {
                var low = picker.minDate ? currentMonthIndex(picker.minDate) : 0;
                var high = picker.maxDate ? (currentMonthIndex(picker.maxDate) + 1) : MONTHS_IN_ALL;
                calendar.months = MONTHS.slice(low, high);
              }


              calendar.getItemAtIndex = function (index) {
                var month = ((index + 1) % 12) || 12;
                var year = YEAR_MIN + Math.floor(index / 12);
                var monthObj = moment(picker.currentDate)
                  .year(year)
                  .month(month);
                return generateMonthCalendar(monthObj);
              };

              calendar.topIndex = currentMonthIndex(picker.currentDate) - calendar.months[0];

              $scope.$watch(function () {
                return picker.currentDate ? picker.currentDate.format('YYYY-MM') : '';
              }, function (val2, val1) {
                if (val2 != val1) {
                  var nDate = moment(val2, 'YYYY-MM');
                  var low = picker.minDate ? currentMonthIndex(picker.minDate) : 0;
                  var index = currentMonthIndex(nDate, low);
                  if (calendar.topIndex != index) {
                    calendar.topIndex = index;
                  }
                }
              });

              var generateMonthCalendar = function (date) {
                var month = {};
                if (date !== null) {
                  month.name = date.format('MMMM YYYY');
                  var startOfMonth = moment(date).locale(picker.params.lang).startOf('month')
                      .hour(date.hour())
                      .minute(date.minute())
                    ;
                  var iNumDay = startOfMonth.format('d');
                  month.days = [];
                  for (var i = startOfMonth.date(); i <= startOfMonth.daysInMonth(); i++) {
                    if (i === startOfMonth.date()) {
                      var iWeek = calendar.week.indexOf(iNumDay.toString());
                      if (iWeek > 0) {
                        for (var x = 0; x < iWeek; x++) {
                          month.days.push(0);
                        }
                      }
                    }
                    month.days.push(moment(startOfMonth).locale(picker.params.lang).date(i));
                  }

                  var daysInAWeek = 7, daysTmp = [], slices = Math.ceil(month.days.length / daysInAWeek);
                  for (var j = 0; j < slices; j++) {
                    daysTmp.push(month.days.slice(j * daysInAWeek, (j + 1) * daysInAWeek));
                  }
                  month.days = daysTmp;
                  return month;
                }

              };

              calendar.toDay = function (i) {
                return moment(parseInt(i), "d")
                  .locale(picker.params.lang)
                  .format("dd")
                  .substring(0, 1);
              };

              calendar.isInRange = function (date) {
                return picker.isAfterMinDate(moment(date), false, false)
                  && picker.isBeforeMaxDate(moment(date), false, false)
                  && picker.isInDisableDates(moment(date));
              };

              calendar.selectDate = function (date) {
                if (date) {
                  if (calendar.isSelectedDay(date)) {
                    return picker.ok();
                  }
                  picker.selectDate(moment(date).hour(calendar.date.hour()).minute(calendar.date.minute()));
                }
              };

              calendar.isSelectedDay = function (m) {
                return m && calendar.date.date() === m.date() && calendar.date.month() === m.month() && calendar.date.year() === m.year();
              };

              calendar.isDateOfTheDay = function (m) {
                var today = calendar.picker.options.showTodaysDate;
                if (!today) {
                  return false;
                }

                return m && today.date() === m.date() && today.month() === m.month() && today.year() === m.year();
              }
            }
          }],
          template: '<md-virtual-repeat-container md-top-index="cal.topIndex" class="months">' +
          '<div md-virtual-repeat="idx in ::cal.months" md-auto-shrink md-item-size="' + ITEM_HEIGHT + '">' +
          '     <div mdc-datetime-picker-calendar-month idx="idx"></div>' +
          '</div>' +
          '</md-virtual-repeat-container>'
        };
      }])
    .directive('mdcDatetimePickerCalendarMonth', ['$compile',
      function ($compile) {
        var buildCalendarContent = function (element, scope) {
          var tbody = angular.element(element[0].querySelector('tbody'));
          var calendar = scope.cal, month = scope.month;

          var tbodyHtml = [];

          month.days.forEach(function (weekDays, i) {
            tbodyHtml.push('<tr>');
            weekDays.forEach(function (weekDay, j) {
              tbodyHtml.push('<td>');
              if (weekDay) {
                if (calendar.isInRange(weekDay)) {
                  //build a
                  var scopeRef = 'month[\'days\'][' + i + '][' + j + ']';

                  tbodyHtml.push('<a href="#" mdc-dtp-noclick class="dtp-select-day" ng-class="{selected: cal.isSelectedDay(' + scopeRef + '), hilite: cal.isDateOfTheDay(' + scopeRef + ')}" ng-click="cal.selectDate(' + scopeRef + ')">');
                  tbodyHtml.push(weekDay.format('D'));
                  tbodyHtml.push('</a>');
                } else {
                  tbodyHtml.push('<span class="dtp-select-day">');
                  tbodyHtml.push(weekDay.format('D'));
                  tbodyHtml.push('</span>');
                }
              }
              tbodyHtml.push('</td>');
            });
            tbodyHtml.push('</tr>');
          });

          tbody.html(tbodyHtml.join(''));
          $compile(tbody)(scope);
        };

        return {
          scope: {
            idx: '='
          },
          require: '^mdcDatetimePickerCalendar',
          restrict: 'AE',
          template: '<div class="dtp-picker-month">{{month.name}}</div>'
          + '<table class="table dtp-picker-days">'
          + '    <thead>'
          + '    <tr>'
          + '        <th ng-repeat="day in cal.week track by $index">{{cal.toDay(day)}}</th>'
          + '    </tr>'
          + '    </thead>'
          + '    <tbody>'
          + '    </tbody>'
          + '</table>',
          link: function (scope, element, attrs, calendar) {
            scope.cal = calendar;
            scope.month = calendar.getItemAtIndex(parseInt(scope.idx));
            buildCalendarContent(element, scope);
            scope.$watch(function () {
              return scope.idx;
            }, function (idx, oldIdx) {
              if (idx != oldIdx) {
                scope.month = calendar.getItemAtIndex(parseInt(scope.idx));
                buildCalendarContent(element, scope);
              }
            });
          }
        };
      }
    ])
  ;

  angular.module(moduleName)
    .directive('mdcDtpNoclick', function () {
      return {
        link: function (scope, el) {
          el.on('click', function (e) {
            e.preventDefault();
          });
        }
      };
    });
  angular.module(moduleName)
    .directive('mdcDatetimePickerClock', [
      function () {

        var template = '<div class="dtp-picker-clock"><span ng-if="!points || points.length < 1">&nbsp;</span>'
          + '<div ng-repeat="point in points" class="dtp-picker-time" ng-style="point.style">'
          + '   <a href="#" mdc-dtp-noclick ng-class="{selected: point.value===currentValue}" class="dtp-select-hour" ng-click="setTime(point.value)" ng-if="pointAvailable(point)">{{point.display}}</a>'
          + '   <a href="#" mdc-dtp-noclick class="disabled dtp-select-hour" ng-if="!pointAvailable(point)">{{point.display}}</a>'
          + '</div>'
          + '<div class="dtp-hand dtp-hour-hand"></div>'
          + '<div class="dtp-hand dtp-minute-hand"></div>'
          + '<div class="dtp-clock-center"></div>'
          + '</div>';

        return {
          restrict: 'E',
          template: template,
          link: function (scope, element, attrs) {
            var minuteMode = attrs.mode === 'minutes';
            var picker = scope.picker;
            //banking on the fact that there will only be one at a time
            var componentRoot = document.querySelector('md-dialog.dtp');
            var exec = function () {
              var clock = angular.element(element[0].querySelector('.dtp-picker-clock')),
                pickerEl = angular.element(componentRoot.querySelector('.dtp-picker'));

              var w = componentRoot.querySelector('.dtp-content').offsetWidth;
              var pl = parseInt(css(pickerEl, 'paddingLeft').replace('px', '')) || 0;
              var pr = parseInt(css(pickerEl, 'paddingRight').replace('px', '')) || 0;
              var ml = parseInt(css(clock, 'marginLeft').replace('px', '')) || 0;
              var mr = parseInt(css(clock, 'marginRight').replace('px', '')) || 0;
              //set width
              var clockWidth = (w - (ml + mr + pl + pr));
              clock.css('width', (clockWidth) + 'px');

              var pL = parseInt(css(pickerEl, 'paddingLeft').replace('px', '')) || 0;
              var pT = parseInt(css(pickerEl, 'paddingTop').replace('px', '')) || 0;
              var mL = parseInt(css(clock, 'marginLeft').replace('px', '')) || 0;
              var mT = parseInt(css(clock, 'marginTop').replace('px', '')) || 0;

              var r = (clockWidth / 2);
              var j = r / 1.2; //???

              var points = [];

              for (var h = 0; h < 12; ++h) {
                var x = j * Math.sin(Math.PI * 2 * (h / 12));
                var y = j * Math.cos(Math.PI * 2 * (h / 12));
                var left = (r + x + pL / 2) - (pL + mL);
                var top = (r - y - mT / 2) - (pT + mT);

                var hour = {
                  left: left,
                  top: top,
                  value: (minuteMode ? (h * 5) : h), //5 for minute 60/12
                  style: {'margin-left': left + 'px', 'margin-top': top + 'px'}
                };

                if (minuteMode) {
                  hour.display = hour.value < 10 ? ('0' + hour.value) : hour.value;
                } else {

                  if (picker.params.shortTime) {
                    hour.display = (h === 0) ? 12 : h;
                  } else {
                    hour.display = picker.isPM() ? h + 12 : h;
                  }
                }


                points.push(hour);
              }

              scope.points = points;
              setCurrentValue();
              clock.css('height', clockWidth + 'px');
              //picker.initHands(true);

              var clockCenter = element[0].querySelector('.dtp-clock-center');
              var centerWidth = (clockCenter.offsetWidth / 2) || 7.5,
                centerHeight = (clockCenter.offsetHeight / 2) || 7.5;
              var _hL = r / 1.8;
              var _mL = r / 1.5;

              angular.element(element[0].querySelector('.dtp-hour-hand')).css({
                left: r + (mL * 1.5) + 'px',
                height: _hL + 'px',
                marginTop: (r - _hL - pL) + 'px'
              }).addClass(!minuteMode ? 'on' : '');

              angular.element(element[0].querySelector('.dtp-minute-hand')).css
              ({
                left: r + (mL * 1.5) + 'px',
                height: _mL + 'px',
                marginTop: (r - _mL - pL) + 'px'
              }).addClass(minuteMode ? 'on' : '');

              angular.element(clockCenter).css({
                left: (r + pL + mL - centerWidth) + 'px',
                marginTop: (r - (mL / 2)) - centerHeight + 'px'
              });
              animateHands();
            };

            var animateHands = function () {
              var _date = picker.currentNearest5Minute();
              var h = _date.hour();
              var m = _date.minute();

              rotateElement(angular.element(element[0].querySelector('.dtp-hour-hand')), (360 / 12) * h);
              var mdg = ((360 / 60) * (5 * Math.round(m / 5)));
              rotateElement(angular.element(element[0].querySelector('.dtp-minute-hand')), mdg);
            };

            var rotateElement = function (el, deg) {
              angular.element(el).css({
                WebkitTransform: 'rotate(' + deg + 'deg)',
                '-moz-transform': 'rotate(' + deg + 'deg)',
                '-ms-transform': 'rotate(' + deg + 'deg)',
                'transform': 'rotate(' + deg + 'deg)'
              });
            };


            var setCurrentValue = function () {
              var date = picker.currentNearest5Minute();
              scope.currentValue = minuteMode ? date.minute() : (date.hour() % 12);
            };

            scope.$watch(function () {
              var tmp = picker.currentNearest5Minute();
              return tmp ? tmp.format('HH:mm') : '';
            }, function () {
              setCurrentValue();
              animateHands();
            });


            var setDisplayPoints = function (isPM, points) {
              for (var i = 0; i < points.length; i++) {
                points[i].display = i;
                if (isPM) {
                  points[i].display += 12;
                }
              }
              return points;
            };

            if (!picker.params.shortTime) {
              scope.$watch('picker.meridien', function () {
                if (!minuteMode) {
                  if (scope.points) {
                    var points = setDisplayPoints(picker.isPM(), angular.copy(scope.points));
                    scope.points = points;
                  }
                }
              });
            }


            scope.setTime = function (val) {
              if (val === scope.currentValue) {
                picker.ok();
              }

              if (!minuteMode) {
                picker.currentDate.hour(picker.isPM() ? (val + 12) : val);
              } else {
                picker.currentDate.minute(val);
              }
              picker.currentDate.second(0)
            };

            scope.pointAvailable = function (point) {
              return minuteMode ? picker.isMinuteAvailable(point.value) : picker.isHourAvailable(point.value);
            };

            var unWatcher = scope.$watch(function () {
              return element[0].querySelectorAll('div').length;
            }, function () {
              exec();
              unWatcher();
            });
          }
        }
      }]);

})();

(function () {
    "use strict";
    /*global angular*/
    /*global ol*/
    angular.module('su-fiscalizacao').directive('imovelFuncional', ['$rootScope', 'RelatorioInfracaoService', imovelFuncional]);

    function imovelFuncional($rootScope, RelatorioInfracaoService) {
        return {
            retrict: 'E',
            templateUrl: "scripts-fiscalizacao/app/directives/relatorio-infracao/imovel-funcional/view/imovel-funcional.html",
            scope: {
                relatorioFiscalizacao: '=',
                autuado: '=',
                tiposDocumento: '=',
                apenasLeitura: '='
            },

            link: function($rootScope) {
                angular.element(document).ready(function() {
                    var scope = $rootScope;

                    /**
                     * Inicia o cadasto do questionário de ocorrência de infracao
                     */
                    scope.init = function(){
                        if(!scope.autuado.ocorrenciaInfracao || typeof scope.autuado.ocorrenciaInfracao === "undefined"){
                            RelatorioInfracaoService.initInfracao(scope.autuado, function(){});
                            scope.initAreasCopy();
                        }

                        if(scope.autuado.ocorrenciaInfracao.id){
                            var result = RelatorioInfracaoService.initOcorrenciaInfracaoExistente(scope.autuado.ocorrenciaInfracao);

                            result.then(function(objectRequest){
                                $rootScope.ocorrenciaInfracao = objectRequest;
                            });

                            if(scope.autuado.ocorrenciaInfracao.perguntaTreze === true){
                                for(var i = 0; i < scope.autuado.documentos.length; i++){
                                    if(scope.autuado.documentos[i].tipoNotificacao === 'NOTIFICACAO_PARA_DESOCUPACAO_EM_90_DIAS'){
                                        scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected = true;
                                    }else{
                                        scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected = false;
                                    }
                                }
                            }

                            RelatorioInfracaoService.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});
                        }

                        scope.isBemDominial = true;
                        
                    };

                    scope.enderecoCopy = angular.copy(scope.relatorioFiscalizacao.demanda.endereco);
                    scope.enderecoCopy.id = null;

                    scope.initAreasCopy = function(){
                        $rootScope.areasInfracao = {};
                        $rootScope.areasInfracao.areaConstrucao = null;
                        $rootScope.areasInfracao.areaObra = null;
                        $rootScope.areasInfracao.areaCercamento = null;
                        $rootScope.areasInfracao.areaAterro = null;
                        $rootScope.areasInfracao.areaEquipamento = null;
                        $rootScope.areasInfracao.areaEquipamento = null;
                        $rootScope.areasInfracao.areaDesmatamento = null;
                        $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                        $rootScope.areasInfracao.areaAutuada = null;
                    };

                    /**
                     * Regras referente ao tipo de  infração para o tipo de imóvel (BEM DE USO DOMINIAL)
                     * @param resp
                     */
                    scope.imovelFuncionalPerguntaUm = function (resp) {
                        if(typeof resp !== "undefined") {
                            !$rootScope.ocorrenciaInfracao ? $rootScope.ocorrenciaInfracao = {} : '';
                            $rootScope.ocorrenciaInfracao.perguntaUmCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaUm(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {
                                    scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy(resp);

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function () {
                                    });
                                });
                            });
                        }
                    };

                    scope.imovelFuncionalPerguntaDois = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaDoisCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaDois(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaDois(resp);

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaDois = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaTres = function (resp) {
                        if(typeof resp !== "undefined") {
                            !$rootScope.ocorrenciaInfracao ? $rootScope.ocorrenciaInfracao = {} : '';
                            $rootScope.ocorrenciaInfracao.perguntaTresCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaTres(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {
                                    scope.populaPerguntaTres(resp);

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function () {
                                    });
                                });
                            });
                        }
                    };

                    scope.populaPerguntaTres = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaQuatro = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaQuatroCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaQuatro(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaQuatro(resp);

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaQuatro = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaCinco = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaCincoCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaCinco(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaCinco(resp);

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaCinco = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaSeis = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaSeisCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaSeis(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaSeis(resp);
                                    scope.areaPerguntaSeis(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaSeis = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy(resp);
                    };

                    scope.areaPerguntaSeis = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }else{
                            $rootScope.areasInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaSete = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaSeteCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaSete(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaSete(resp);
                                    scope.areaPerguntaSete(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaSete = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy(resp);
                    };

                    scope.areaPerguntaSete = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }else{
                            scope.autuado.ocorrenciaInfracao.perguntaQuatro === true ? scope.areasInfracao.areaObra = $rootScope.areasInfracao.areaObra : $rootScope.areasInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaOito = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaOitoCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaOito(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaOito(resp);
                                    scope.areaPerguntaOito(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao,function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaOito = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy(resp);
                    };

                    scope.areaPerguntaOito = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento ? scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento : scope.autuado.ocorrenciaInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;

                        }else{
                            scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                            $rootScope.areasInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaNove = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaNoveCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaNove(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaNove(resp);
                                    scope.areaPerguntaNove(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaNove = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy(resp);
                    };

                    scope.areaPerguntaNove = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento ? scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento : scope.autuado.ocorrenciaInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias ? scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias : scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }else{
                            scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                            scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaDez = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaDezCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaDez(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaDez(resp);
                                    scope.areaPerguntaDez(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaDez = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy($rootScope.ocorrenciaInfracao.perguntaNoveCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDez = angular.copy(resp);
                    };

                    scope.areaPerguntaDez = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;;
                            $rootScope.areasInfracao.areaEquipamento ? scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento : scope.autuado.ocorrenciaInfracao.areaEquipamento = null;;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias ? scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias : scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = null;;
                            $rootScope.areasInfracao.areaDesmatamento ? scope.autuado.ocorrenciaInfracao.areaDesmatamento = $rootScope.areasInfracao.areaDesmatamento : scope.autuado.ocorrenciaInfracao.areaDesmatamento = null;;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }else{
                            scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                            scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento;
                            scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaOnze = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaOnze(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaOnze(resp);
                                    scope.areaPerguntaOnze(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaOnze = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy($rootScope.ocorrenciaInfracao.perguntaNoveCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDez = angular.copy($rootScope.ocorrenciaInfracao.perguntaDezCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOnze = angular.copy(resp);
                    };

                    scope.areaPerguntaOnze = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento ? scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento : scope.autuado.ocorrenciaInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias ? scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias : scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento ? scope.autuado.ocorrenciaInfracao.areaDesmatamento = $rootScope.areasInfracao.areaDesmatamento : scope.autuado.ocorrenciaInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento ? scope.autuado.ocorrenciaInfracao.areaCercamento = $rootScope.areasInfracao.areaCercamento : scope.autuado.ocorrenciaInfracao.areaCercamento = null;
                        }else{
                            scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                            scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento;
                            scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias;
                            scope.autuado.ocorrenciaInfracao.areaDesmatamento = $rootScope.areasInfracao.areaDesmatamento;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaDoze = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaDozeCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaDoze(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaDoze(resp);
                                    scope.areaFim();

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaDoze = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy($rootScope.ocorrenciaInfracao.perguntaNoveCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDez = angular.copy($rootScope.ocorrenciaInfracao.perguntaDezCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOnze = angular.copy($rootScope.ocorrenciaInfracao.perguntaOnzeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDoze = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaTreze = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaTreze(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaTreze(resp);
                                    scope.areaFim();

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao,function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaTreze = function (resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy($rootScope.ocorrenciaInfracao.perguntaNoveCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDez = angular.copy($rootScope.ocorrenciaInfracao.perguntaDezCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOnze = angular.copy($rootScope.ocorrenciaInfracao.perguntaOnzeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDoze = angular.copy($rootScope.ocorrenciaInfracao.perguntaDozeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTreze = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaQuatorze = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaQuatorze(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaQuatorze(resp);
                                    scope.areaFim();

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao,function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaQuatorze = function (resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy($rootScope.ocorrenciaInfracao.perguntaNoveCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDez = angular.copy($rootScope.ocorrenciaInfracao.perguntaDezCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOnze = angular.copy($rootScope.ocorrenciaInfracao.perguntaOnzeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDoze = angular.copy($rootScope.ocorrenciaInfracao.perguntaDozeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTreze = angular.copy($rootScope.ocorrenciaInfracao.perguntaTrezeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatorze = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaQuinze = function(resp) {
                        if (typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = angular.copy(resp);

                            RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                scope.populaPerguntaQuinze(resp);
                                scope.areaFim();

                                //Calcula o tamanho da área autuada com base nos campos preenchidos
                                scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao,function(){});

                                //Se necessário gera os documentos necessários para a questão do questionário
                                scope.geraDocumentosDominial(function(){});
                            });
                        }
                    };

                    scope.populaPerguntaQuinze = function (resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy($rootScope.ocorrenciaInfracao.perguntaNoveCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDez = angular.copy($rootScope.ocorrenciaInfracao.perguntaDezCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOnze = angular.copy($rootScope.ocorrenciaInfracao.perguntaOnzeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDoze = angular.copy($rootScope.ocorrenciaInfracao.perguntaDozeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTreze = angular.copy($rootScope.ocorrenciaInfracao.perguntaTrezeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatorze = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuinze = angular.copy(resp);
                    };

                    scope.areaFim = function(){
                        scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                        scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento;
                        scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias;
                        scope.autuado.ocorrenciaInfracao.areaDesmatamento = $rootScope.areasInfracao.areaDesmatamento;
                        scope.autuado.ocorrenciaInfracao.areaCercamento = $rootScope.areasInfracao.areaCercamento;
                    };

                    /**
                     *  Gera os documentos necessários para o tipo de imóvel (BEM DOMINIAL)
                     */
                    scope.geraDocumentosDominial = function () {
                        scope.autuado.documentos = [];

                        var documento = {};

                        if (scope.autuado.ocorrenciaInfracao.perguntaUm !== null && scope.autuado.ocorrenciaInfracao.perguntaUm === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao = 'NOTIFICACAO_PARA_COMPLEMENTACAO_DE_DOCUMENTACAO';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaDois !== null && scope.autuado.ocorrenciaInfracao.perguntaDois === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaTres !== null && scope.autuado.ocorrenciaInfracao.perguntaTres === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao = 'NOTIFICACAO_PARA_COMPLEMENTACAO_DE_DOCUMENTACAO';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaQuatro !== null && scope.autuado.ocorrenciaInfracao.perguntaQuatro === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao = 'NOTIFICACAO_PARA_COMPLEMENTACAO_DE_DOCUMENTACAO';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                                
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaCinco !== null && scope.autuado.ocorrenciaInfracao.perguntaCinco === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao = 'NOTIFICACAO_PARA_COMPLEMENTACAO_DE_DOCUMENTACAO';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaSeis !== null && scope.autuado.ocorrenciaInfracao.perguntaSeis === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.embargo = true;
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.sancaoAdministrativa.desocupacao = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }

                                if (!scope.autuado.existeEmbargo) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_EMBARGO';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaSete !== null && scope.autuado.ocorrenciaInfracao.perguntaSete === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.sancaoAdministrativa.desocupacao = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaOito !== null && scope.autuado.ocorrenciaInfracao.perguntaOito === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.sancaoAdministrativa.desocupacao = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaNove !== null && scope.autuado.ocorrenciaInfracao.perguntaNove === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.sancaoAdministrativa.desocupacao = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaDez !== null && scope.autuado.ocorrenciaInfracao.perguntaDez === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaOnze !== null && scope.autuado.ocorrenciaInfracao.perguntaOnze === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.sancaoAdministrativa.desocupacao = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaDoze !== null && scope.autuado.ocorrenciaInfracao.perguntaDoze === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_30_DIAS';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaTreze !== null && scope.autuado.ocorrenciaInfracao.perguntaTreze === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao  = 'NOTIFICACAO_PARA_DESOCUPACAO_IMEDIATA';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }
                        if (scope.autuado.ocorrenciaInfracao.perguntaQuatorze !== null && scope.autuado.ocorrenciaInfracao.perguntaQuatorze === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao  = 'NOTIFICACAO_PARA_DESOCUPACAO_IMEDIATA';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaQuinze !== null && scope.autuado.ocorrenciaInfracao.perguntaQuinze === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.endereco = scope.enderecoCopy;

                                    if(scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected === true){
                                        documento.tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_90_DIAS';
                                    }else{
                                        documento.tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_180_DIAS';
                                    }

                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }
                        
                    };

                    scope.handleTipoNotificacaoSelected = function(resp){
                      for(var i = 0; i < scope.autuado.documentos.length; i++){
                          if(scope.autuado.documentos[i].tipoDocumento === 'NOTIFICACAO'){
                              if(resp === false){
                                  scope.autuado.documentos[i].tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_180_DIAS';
                              }else{
                                  scope.autuado.documentos[i].tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_90_DIAS';
                              }
                          }
                      }
                    };

                    /**
                     * Prepara o documento para a sua visualização
                     * @param documento
                     * @param event
                     */
                    scope.visualizarDocumento = function (documento, event) {
                        documento.apenasLeitura = true;

                        RelatorioInfracaoService.openModal(event, documento, scope.relatorioFiscalizacao, scope.autuado.responsavel, scope.relatorioFiscalizacao.inicio, scope);
                    };

                    /**
                     * Prepara o documento para a sua edição
                     * @param documento
                     * @param index
                     * @param ev
                     */
                    scope.updateDocumentoDominial = function (documento, index, ev) {
                        if(documento.id){
                            documento = documento;
                            documento.index = index;
                            documento.isUpdate = true;
                            documento.apenasLeitura = false;

                            RelatorioInfracaoService.openModalJustificativa(ev, documento, scope.relatorioFiscalizacao, scope.autuado, scope.relatorioFiscalizacao.inicio, 'edicao', scope);
                        }else{
                            RelatorioInfracaoService.openModal(event, documento, scope.relatorioFiscalizacao, scope.autuado.responsavel, scope.relatorioFiscalizacao.inicio, scope);
                        }
                    };

                    /**
                     * Prepara o documento para a sua edição
                     * @param documento
                     * @param index
                     * @param ev
                     */
                    scope.excluirDocumentoDominial = function(documento, index, ev) {
                        documento = documento;
                        documento.index = index;
                        documento.isUpdate = true;
                        documento.apenasLeitura = false;

                        RelatorioInfracaoService.openModalJustificativa(ev, documento, scope.relatorioFiscalizacao, scope.autuado, scope.relatorioFiscalizacao.inicio, 'excluir', scope);
                    };

                    scope.calculaAreaAutuada = function (ocorrenciaInfracao, callback) {
                        !$rootScope.areasInfracao ? $rootScope.areasInfracao = {} : "";

                        $rootScope.areasInfracao.areaConstrucao = angular.copy(ocorrenciaInfracao.areaConstrucao);
                        $rootScope.areasInfracao.areaObra = angular.copy(ocorrenciaInfracao.areaObra);
                        $rootScope.areasInfracao.areaCercamento = angular.copy(ocorrenciaInfracao.areaCercamento);
                        $rootScope.areasInfracao.areaAterro = angular.copy(ocorrenciaInfracao.areaAterro);
                        $rootScope.areasInfracao.areaEquipamento = angular.copy(ocorrenciaInfracao.areaEquipamento);
                        $rootScope.areasInfracao.areaDesmatamento = angular.copy(ocorrenciaInfracao.areaDesmatamento);
                        $rootScope.areasInfracao.areaOutrasBenfeitorias = angular.copy(ocorrenciaInfracao.areaOutrasBenfeitorias);

                        RelatorioInfracaoService.calculaAreaAutuada(ocorrenciaInfracao, function () {

                            if (callback) {
                                callback();
                            }
                        });
                    };

                    scope.init();
                });
            }
        }
    }
})();

(function () {
    "use strict";
    /*global angular*/
    /*global ol*/
    angular.module('su-fiscalizacao').directive('bemUsoEspecial', ['$rootScope', 'RelatorioInfracaoService',  bemUsoEspecial]);

    function bemUsoEspecial($rootScope, RelatorioInfracaoService) {
        return {
            retrict: 'E',
            templateUrl: "scripts-fiscalizacao/app/directives/relatorio-infracao/bem-uso-especial/view/bem-uso-especial.html",
            scope: {
                relatorioFiscalizacao: '=',
                autuado: '=',
                tiposDocumento: '='
            },

            link: function ($rootScope) {
                angular.element(document).ready(function () {
                    var scope = $rootScope;

                    scope.numPerguntas = ['Um', 'Dois', 'Tres', 'Quatro', 'Cinco', 'Seis', 'Sete', 'Oito', 'Nove', 'Dez', 'Onze', 'Doze', 'Treze', 'Quatorze', 'Quinze'];

                    /**
                     * Inicia o cadasto do questionário de ocorrência de infracao
                     */
                    scope.init = function(){
                        if(!scope.autuado.ocorrenciaInfracao || typeof scope.autuado.ocorrenciaInfracao === "undefined"){
                            RelatorioInfracaoService.initInfracao(scope.autuado, function(){});
                            scope.initAreasCopy();
                        }

                        if(scope.autuado.ocorrenciaInfracao.id){
                            var result = RelatorioInfracaoService.initOcorrenciaInfracaoExistente(scope.autuado.ocorrenciaInfracao)

                            result.then(function(objectRequest){
                                $rootScope.ocorrenciaInfracao = objectRequest;
                            });

                            if(scope.autuado.ocorrenciaInfracao.perguntaQuinze === true){
                                for(var i = 0; i < scope.autuado.documentos.length; i++){
                                    if(scope.autuado.documentos[i].tipoNotificacao === 'NOTIFICACAO_PARA_DESOCUPACAO_EM_90_DIAS'){
                                        scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected = true;
                                    }else{
                                        scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected = false;
                                    }
                                }
                            }

                            RelatorioInfracaoService.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});
                        }

                        scope.isBemEspecial = true;
                    };

                    scope.enderecoCopy = angular.copy(scope.relatorioFiscalizacao.demanda.endereco);
                    scope.enderecoCopy.id = null;

                    scope.initAreasCopy = function(){
                        $rootScope.areasInfracao = {};
                        $rootScope.areasInfracao.areaConstrucao = null;
                        $rootScope.areasInfracao.areaObra = null;
                        $rootScope.areasInfracao.areaCercamento = null;
                        $rootScope.areasInfracao.areaAterro = null;
                        $rootScope.areasInfracao.areaEquipamento = null;
                        $rootScope.areasInfracao.areaEquipamento = null;
                        $rootScope.areasInfracao.areaDesmatamento = null;
                        $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                        $rootScope.areasInfracao.areaAutuada = null;
                    };

                    /**
                     * Regras referente ao tipo de infração para o tipo de imóvel (BEM DE USO ESPECIAL)
                     * @param resp
                     */

                    scope.isPerguntaRespondida = function(pergunta) {
                        return (typeof pergunta !== 'undefined' && pergunta !== null)
                    }

                    scope.isBemUsoDominialObrigatorio = function(pergunta) {
                        return scope.autuado.ocorrenciaInfracao.perguntaDois && pergunta;
                    }

                    scope.isBemUsoEspecialObrigatorio = function(pergunta) {
                        return !scope.autuado.ocorrenciaInfracao.perguntaDois && pergunta;
                    }


                    scope.bemUsoDominialPergunta = function (resp, num) {
                        scope.autuado.ocorrenciaInfracao['pergunta' + num] = resp;
                        scope.geraDocumentosBemDominial(num);
                        scope.limparPerguntasBemDominial(num);

                    };

                    scope.bemUsoEspecialPergunta = function (resp, num) {
                        scope.autuado.ocorrenciaInfracao['pergunta' + num] = resp;
                        scope.geraDocumentosBemEspecial(num);
                        scope.limparPerguntasBemEspecial(num);

                    };

                    scope.existeTipoDocumento = function(tipoDocumento){
                        if(tipoDocumento === 'NOTIFICACAO')
                            return scope.autuado.existeNotificacao;
                        if(tipoDocumento === 'AUTO_DE_EMBARGO')
                            return scope.autuado.existeEmbargo;
                        if(tipoDocumento === 'AUTO_DE_INFRACAO')
                            return scope.autuado.existeInfracao;
                    }

                    scope.limparPerguntasBemDominial = function (num){

                        var limpar = false;
                        scope.numPerguntas.forEach(function(numPer){
                            if(limpar)
                                scope.autuado.ocorrenciaInfracao['pergunta' + numPer] = null;
                            if(numPer === num)
                                limpar = true;
                        });

                        if(scope.numPerguntas.indexOf(num)  <= 4)
                            scope.autuado.ocorrenciaInfracao.areaObra = null;
                        if(scope.numPerguntas.indexOf(num)  === 5 && !scope.autuado.ocorrenciaInfracao.perguntaCinco)
                            scope.autuado.ocorrenciaInfracao.areaObra = null;
                        else if(scope.numPerguntas.indexOf(num)  <= 6)
                            scope.autuado.ocorrenciaInfracao.areaEquipamento = null;
                        else if(scope.numPerguntas.indexOf(num)  <= 7)
                            scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = null;
                        else if(scope.numPerguntas.indexOf(num)  <= 8)
                            scope.autuado.ocorrenciaInfracao.areaDesmatamento = null;
                        else if(scope.numPerguntas.indexOf(num)  <= 9)
                            scope.autuado.ocorrenciaInfracao.areaCercamento = null;
                            else if(scope.numPerguntas.indexOf(num)  <= 13)
                            scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected = null;

                    };

                    scope.geraDocumentosBemDominial = function (num) {
                        scope.autuado.documentos = [];

                        var getTiposDocumento = function(num){
                            if([ 'Tres', 'Quatro', 'Onze', 'Doze', 'Treze', 'Quatorze'].indexOf(num) > -1)
                                return ['NOTIFICACAO'];    // existe notificacao
                            if(num === 'Cinco')
                                return ['AUTO_DE_EMBARGO', 'AUTO_DE_INFRACAO'];
                            if(['Seis', 'Sete', 'Oito', 'Nove', 'Dez'].indexOf(num) > -1)
                                return ['AUTO_DE_INFRACAO'] // existe auto de infracao
                            return [];
                        }

                        var getTipoNotificacao = function(num, tipoNotificacaoSelected){
                            if(['Quatro'].indexOf(num) > -1)
                                return 'NOTIFICACAO_PARA_COMPLEMENTACAO_DE_DOCUMENTACAO';
                            if(num === 'Quatorze')
                                if(tipoNotificacaoSelected)
                                    return 'NOTIFICACAO_PARA_DESOCUPACAO_EM_90_DIAS';
                                else
                                    return 'NOTIFICACAO_PARA_DESOCUPACAO_EM_180_DIAS';
                        }

			
                        var getSancaoAdmin = function(num, tipoDoc){
							
							if(tipoDoc !== 'AUTO_DE_INFRACAO')
							return;

                            if(num === 'Cinco')
                                return { embargo: true, multa: true, desocupacao: true };
                            if(['Seis', 'Sete', 'Oito', 'Dez'].indexOf(num) > -1)
                                return { multa: true, desocupacao: true };
                            if(num === 'Nove')
                                return { multa: true };
                        }

                        if(scope.autuado.ocorrenciaInfracao['pergunta' + num]){
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                var tiposDocumento = getTiposDocumento(num);

                                tiposDocumento.forEach(function(tipoDoc){
                                    var existeTipoDoc = scope.existeTipoDocumento(tipoDoc);

                                    if(!existeTipoDoc){
                                        var documento = {};
                                        documento.tipoDocumento = tipoDoc;
                                        documento.tipoNotificacao = getTipoNotificacao(num, scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected);
                                        documento.endereco = scope.enderecoCopy;
                                        documento.sancaoAdministrativa = getSancaoAdmin(num, tipoDoc);
                                        scope.autuado.documentos.push(documento);
                                    }
                                });

                            });
                        }

                    };


                    scope.limparPerguntasBemEspecial = function (num){
                        var limpar = false;

                        scope.numPerguntas.forEach(function(numPer){
                            if(limpar)
                                scope.autuado.ocorrenciaInfracao['pergunta' + numPer] = null;
                            if(numPer === num)
                                limpar = true;
                        });

                        if(scope.numPerguntas.indexOf(num)  <= 4)
                            scope.autuado.ocorrenciaInfracao.areaObra = null;
                        if(scope.numPerguntas.indexOf(num)  === 5 && !scope.autuado.ocorrenciaInfracao.perguntaCinco)
                            scope.autuado.ocorrenciaInfracao.areaObra = null;
                        else if(scope.numPerguntas.indexOf(num)  <= 6)
                            scope.autuado.ocorrenciaInfracao.areaEquipamento = null;
                        else if(scope.numPerguntas.indexOf(num)  <= 7)
                            scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = null;
                        else if(scope.numPerguntas.indexOf(num)  <= 8)
                            scope.autuado.ocorrenciaInfracao.areaDesmatamento = null;
                        else if(scope.numPerguntas.indexOf(num)  <= 9)
                            scope.autuado.ocorrenciaInfracao.areaCercamento = null;
                            else if(scope.numPerguntas.indexOf(num)  <= 13)
                            scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected = null;

                    };

                    scope.geraDocumentosBemEspecial = function (num) {
                        scope.autuado.documentos = [];

                        var getTiposDocumento = function(num){

                            if(['Um', 'Dois', 'Tres', 'Quatro', 'Cinco'].indexOf(num) > -1)
                                return ['NOTIFICACAO'];
                            return [];
                        }

                        if(scope.autuado.ocorrenciaInfracao['pergunta' + num]){
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                var tiposDocumento = getTiposDocumento(num);

                                tiposDocumento.forEach(function(tipoDoc){
                                    var existeTipoDoc = scope.existeTipoDocumento(tipoDoc);

                                    if(!existeTipoDoc){
                                        var documento = {};
                                        documento.tipoDocumento = tipoDoc;
                                        documento.tipoNotificacao = 'NOTIFICACAO_PARA_COMPLEMENTACAO_DE_DOCUMENTACAO';
                                        documento.endereco = scope.enderecoCopy;
                                        scope.autuado.documentos.push(documento);
                                    }
                                });

                            });
                        }

                    };





                    scope.areaFim = function(){
                        scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                        scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento;
                        scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias;
                        scope.autuado.ocorrenciaInfracao.areaDesmatamento = $rootScope.areasInfracao.areaDesmatamento;
                        scope.autuado.ocorrenciaInfracao.areaCercamento = $rootScope.areasInfracao.areaCercamento;
                    };

                    /**
                     *  Gera os documentos necessários para o tipo de imóvel (BEM DE USO ESPECIAL)
                     */


                    scope.handleTipoNotificacaoSelected = function(resp){
                        for(var i = 0; i < scope.autuado.documentos.length; i++){
                            if(scope.autuado.documentos[i].tipoDocumento === 'NOTIFICACAO'){
                                if(resp === false){
                                    scope.autuado.documentos[i].tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_180_DIAS';
                                }else{
                                    scope.autuado.documentos[i].tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_90_DIAS';
                                }
                            }
                        }
                    };

                    /**
                     * Prepara o documento para a sua visualização
                     * @param documento
                     * @param event
                     */
                    scope.visualizarDocumento = function(documento, event){
                        documento.apenasLeitura = true;

                        RelatorioInfracaoService.openModal(event, documento, scope.relatorioFiscalizacao, scope.autuado.responsavel, scope.relatorioFiscalizacao.inicio, scope);
                    };

                    /**
                     * Prepara o documento para a sua edição
                     * @param documento
                     * @param index
                     * @param ev
                     */
                    scope.updateDocumento = function(documento, index, ev) {
                        if(documento.id){
                            documento = documento;
                            documento.index = index;
                            documento.isUpdate = true;
                            documento.apenasLeitura = false;

                            RelatorioInfracaoService.openModalJustificativa(ev, documento, scope.relatorioFiscalizacao, scope.autuado, scope.relatorioFiscalizacao.inicio, 'edicao', scope);
                        }else{
                            RelatorioInfracaoService.openModal(event, documento, scope.relatorioFiscalizacao, scope.autuado.responsavel, scope.relatorioFiscalizacao.inicio, scope);
                        }
                    };

                    /**
                     * Prepara o documento para a sua edição
                     * @param documento
                     * @param index
                     * @param ev
                     */
                    scope.excluirDocumento = function(documento, index, ev) {
                        documento = documento;
                        documento.index = index;
                        documento.isUpdate = true;
                        documento.apenasLeitura = false;

                        RelatorioInfracaoService.openModalJustificativa(ev, documento, scope.relatorioFiscalizacao, scope.autuado, scope.relatorioFiscalizacao.inicio, 'excluir', scope);
                    };

                    /**
                     * calcula o tamanho da área autuada
                     * @param ocorrenciaInfracao
                     * @param callback
                     */

                    scope.calculaAreaAutuada = function(ocorrenciaInfracao, callback){
                        !$rootScope.areasInfracao ? $rootScope.areasInfracao = {} : "";

                        $rootScope.areasInfracao.areaConstrucao = angular.copy(ocorrenciaInfracao.areaConstrucao);
                        $rootScope.areasInfracao.areaObra = angular.copy(ocorrenciaInfracao.areaObra);
                        $rootScope.areasInfracao.areaCercamento = angular.copy(ocorrenciaInfracao.areaCercamento);
                        $rootScope.areasInfracao.areaAterro = angular.copy(ocorrenciaInfracao.areaAterro);
                        $rootScope.areasInfracao.areaEquipamento = angular.copy(ocorrenciaInfracao.areaEquipamento);
                        $rootScope.areasInfracao.areaDesmatamento = angular.copy(ocorrenciaInfracao.areaDesmatamento);
                        $rootScope.areasInfracao.areaOutrasBenfeitorias = angular.copy(ocorrenciaInfracao.areaOutrasBenfeitorias);

                        RelatorioInfracaoService.calculaAreaAutuada(ocorrenciaInfracao, function(){

                            if(callback){
                                callback();
                            }
                        });
                    };

                    scope.init();
                });
            }
        };
    }
}());

(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('su-fiscalizacao').directive('bemUsoComum', ['$rootScope', 'RelatorioInfracaoService', '$q', 'RelatorioService',  bemUsoComum]);

    function bemUsoComum ($rootScope, RelatorioInfracaoService, $q, RelatorioService) {
        return {
            retrict: 'E',
            templateUrl: 'scripts-fiscalizacao/app/directives/relatorio-infracao/bem-uso-comum/view/bem-uso-comum.html',
            scope: {
                relatorioFiscalizacao: '=',
                autuado: '=',
                tiposDocumento: '=',
                apenasLeitura: '='
            },

            link: function ($rootScope) {
                angular.element(document).ready(function () {
                    var scope = $rootScope;
                    scope.numPerguntas = [ 'Um', 'Dois', 'Tres', 'Quatro', 'Cinco', 'Seis', 'Sete', 'Oito', 'Nove', 'Dez', 'Onze', 'Doze', 'Treze', 'Quatorze', 'Quinze'];
                    /**
                     * Inicia o cadasto do questionário de ocorrência de infracao
                     */
                    scope.init = function(){
                        if(!scope.autuado.ocorrenciaInfracao || typeof scope.autuado.ocorrenciaInfracao === "undefined"){
                            RelatorioInfracaoService.initInfracao(scope.autuado, function(){});
                            scope.initAreasCopy();
                        }

                        if(scope.autuado.ocorrenciaInfracao.id){
                            var result = RelatorioInfracaoService.initOcorrenciaInfracaoExistente(scope.autuado.ocorrenciaInfracao);

                            result.then(function(objectRequest){
                                $rootScope.ocorrenciaInfracao = objectRequest;
                            });

                            RelatorioInfracaoService.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});
                        }

                        scope.isBemUsoComum = true;
                    };

                    scope.enderecoCopy = angular.copy(scope.relatorioFiscalizacao.demanda.endereco);
                    scope.enderecoCopy.id = null;

                    scope.initAreasCopy = function(){
                        $rootScope.areasInfracao = {};
                        $rootScope.areasInfracao.areaConstrucao = null;
                        $rootScope.areasInfracao.areaConstruida = null;
                        $rootScope.areasInfracao.areaObra = null;
                        $rootScope.areasInfracao.areaCercamento = null;
                        $rootScope.areasInfracao.areaAterro = null;
                        $rootScope.areasInfracao.areaEquipamento = null;
                        $rootScope.areasInfracao.areaDesmatamento = null;
                        $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                        $rootScope.areasInfracao.areaAutuada = null;
                    };



                    scope.isPerguntaRespondida = function(pergunta) {
                        return (typeof pergunta !== 'undefined' && pergunta !== null)
                    }

                    scope.changePergunta = function (resp, num) {

                        scope.autuado.ocorrenciaInfracao['pergunta' + num] = resp;
                        scope.geraDocumentos(num);
                        scope.limparPerguntas(num);
                    };



                    scope.existeTipoDocumento = function(tipoDocumento){
                        if(tipoDocumento === 'NOTIFICACAO')
                            return scope.autuado.existeNotificacao;
                        if(tipoDocumento === 'AUTO_DE_EMBARGO')
                            return scope.autuado.existeEmbargo;
                        if(tipoDocumento === 'AUTO_DE_INFRACAO')
                            return scope.autuado.existeInfracao;
                    }

                    scope.limparPerguntas = function (num){

                        var limpar = false;
                        scope.numPerguntas.forEach(function(numPer){
                            if(limpar)
                                scope.autuado.ocorrenciaInfracao['pergunta' + numPer] = null;
                            if(numPer === num)
                                limpar = true;
                        });

                        if(scope.numPerguntas.indexOf(num)  <= 6)
                            scope.autuado.ocorrenciaInfracao.areaConstruida = null;

                    };


                    scope.geraDocumentos = function (num) {

                        scope.autuado.documentos = [];
                        

                        var getTiposDocumento = function(num){
                            if(['Um', 'Dois', 'Tres', 'Quatro'].indexOf(num) > -1)
                                return ['NOTIFICACAO'];
                            if(num === 'Cinco')
                                return ['NOTIFICACAO','AUTO_DE_EMBARGO'];
                            if(['Seis', 'Sete'].indexOf(num) > -1)
                                return ['AUTO_DE_EMBARGO', 'AUTO_DE_INFRACAO'];
                            if(['Oito','Nove', 'Dez', 'Onze', 'Doze', 'Treze'].indexOf(num) > -1)
                                return ['AUTO_DE_INFRACAO'];
                            return [];
                        }

                        var getUltimaRespostaPositivaApartirDa5 = function(num) {							
							var idx = scope.numPerguntas.indexOf(num);
                            for (idx; idx >= 4 ; idx--) {
                                if(scope.autuado.ocorrenciaInfracao['pergunta' + scope.numPerguntas[idx]])
									return scope.numPerguntas[idx];
							}     														
                        }

                        var getTipoNotificacao = function(num){
                            if(['Um', 'Dois', 'Cinco'].indexOf(num) > -1)
                                return 'NOTIFICACAO_PARA_COMPLEMENTACAO_DE_DOCUMENTACAO';
                            if(num === 'Tres')
                                return 'NOTIFICACAO_PARA_REGULARIZACAO_EM_60_DIAS';
                            if(num === 'Quatro')
                                return 'NOTIFICACAO_PARA_O_MUNICIPIO';

                        }

                        var getSancaoAdmin = function(num, tipoDoc){
                            if(tipoDoc !== 'AUTO_DE_INFRACAO')
                                return;
                            if(['Seis'].indexOf(num) > -1)
                                return { embargo: true, multa: true, desocupacao: true };
                            if(['Nove', 'Dez', 'Onze', 'Treze'].indexOf(num) > -1)
                                return { multa: true, desocupacao: true };
                            if(num === 'Doze')
								return { multa: true };								
							if(num === 'Sete')
								return { embargo: true, demolicao: true, multa: true };								
							if(num === 'Oito')
							return { demolicao: true, multa: true };								
                        }

                        var addDocumento = function(num) {
                            var tiposDocumento = getTiposDocumento(num);

                                tiposDocumento.forEach(function(tipoDoc){
                                    var existeTipoDoc = scope.existeTipoDocumento(tipoDoc);

                                    if(!existeTipoDoc){
                                        var documento = {};
                                        documento.tipoDocumento = tipoDoc;
                                        documento.tipoNotificacao = getTipoNotificacao(num, scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected);
                                        documento.endereco = scope.enderecoCopy;
                                        documento.sancaoAdministrativa = getSancaoAdmin(num, tipoDoc);
                                        scope.autuado.documentos.push(documento);
                                    }
                                });
                        }

                        if(scope.autuado.ocorrenciaInfracao['pergunta' + num]){
                            addDocumento(num);
                        }else if(scope.numPerguntas.indexOf(num) > 4){
                            // se a resposta for 6 em diante toda resposta "Não" mantem a resposta anterior
                            var numUltimaPergunta = getUltimaRespostaPositivaApartirDa5(num);
                            addDocumento(numUltimaPergunta);                            
                        }

                    };


                    /**
                     * Prepara o documento para a sua visualização
                     * @param documento
                     * @param event
                     */
                    scope.visualizarDocumento = function(documento, event){
                        documento.apenasLeitura = true;
                        RelatorioInfracaoService.openModal(event, documento, scope.relatorioFiscalizacao, scope.autuado.responsavel, scope.relatorioFiscalizacao.inicio, scope);
                    };

                    /**
                     * Prepara o documento para a sua edição
                     * @param documento
                     * @param index
                     * @param ev
                     */
                    scope.updateDocumento = function(documento, index, ev) {
                        if(documento.id){
                            documento = documento;
                            documento.index = index;
                            documento.isUpdate = true;
                            documento.apenasLeitura = false;

                            RelatorioInfracaoService.openModalJustificativa(ev, documento, scope.relatorioFiscalizacao, scope.autuado, scope.relatorioFiscalizacao.inicio, 'edicao', scope);
                        }else{
                            RelatorioInfracaoService.openModal(event, documento, scope.relatorioFiscalizacao, scope.autuado.responsavel, scope.relatorioFiscalizacao.inicio, scope);
                        }
                    };

                    /**
                     * Prepara o documento para a sua edição
                     * @param documento
                     * @param index
                     * @param ev
                     */
                    scope.excluirDocumento = function(documento, index, ev) {
                        documento = documento;
                        documento.index = index;
                        documento.isUpdate = true;
                        documento.apenasLeitura = false;

                        RelatorioInfracaoService.openModalJustificativa(ev, documento, scope.relatorioFiscalizacao, scope.autuado, scope.relatorioFiscalizacao.inicio, 'excluir', scope);
                    };

                    /**
                     * calcula o tamanho da área autuada
                     * @param ocorrenciaInfracao
                     * @param callback
                     */
                    scope.calculaAreaAutuada = function(ocorrenciaInfracao, callback){
                        $rootScope.areasInfracao.areaConstruida = angular.copy(ocorrenciaInfracao.areaConstruida);
                        $rootScope.areasInfracao.areaConstrucao = angular.copy(ocorrenciaInfracao.areaConstrucao);
                        $rootScope.areasInfracao.areaObra = angular.copy(ocorrenciaInfracao.areaObra);
                        $rootScope.areasInfracao.areaCercamento = angular.copy(ocorrenciaInfracao.areaCercamento);
                        $rootScope.areasInfracao.areaAterro = angular.copy(ocorrenciaInfracao.areaAterro);
                        $rootScope.areasInfracao.areaEquipamento = angular.copy(ocorrenciaInfracao.areaEquipamento);
                        $rootScope.areasInfracao.areaDesmatamento = angular.copy(ocorrenciaInfracao.areaDesmatamento);
                        $rootScope.areasInfracao.areaOutrasBenfeitorias = angular.copy(ocorrenciaInfracao.areaOutrasBenfeitorias);

                        RelatorioInfracaoService.calculaAreaAutuada(ocorrenciaInfracao, function(){

                            if(callback){
                                callback();
                            }
                        });
                    };

                    scope.init();
                });
            }
        }
    }
})();

(function () {
    "use strict";
    /*global angular*/
    /*global ol*/
    angular.module('su-fiscalizacao').directive('bemDominial', ['$rootScope', 'RelatorioInfracaoService',  bemDominial]);

    function bemDominial($rootScope, RelatorioInfracaoService) {
        return {
            retrict: 'E',
            templateUrl: "scripts-fiscalizacao/app/directives/relatorio-infracao/bem-dominial/view/bem-dominial.html",
            scope: {
                relatorioFiscalizacao: '=',
                autuado: '=',
                tiposDocumento: '='
            },

            link: function ($rootScope) {
                angular.element(document).ready(function () {
                    var scope = $rootScope;

                    /**
                     * Inicia o cadasto do questionário de ocorrência de infracao
                     */
                    scope.init = function(){
                        if(!scope.autuado.ocorrenciaInfracao || typeof scope.autuado.ocorrenciaInfracao === "undefined"){
                            RelatorioInfracaoService.initInfracao(scope.autuado, function(){});
                            scope.initAreasCopy();
                        }

                        if(scope.autuado.ocorrenciaInfracao.id){
                            var result = RelatorioInfracaoService.initOcorrenciaInfracaoExistente(scope.autuado.ocorrenciaInfracao);

                            result.then(function(objectRequest){
                                $rootScope.ocorrenciaInfracao = objectRequest;
                            });

                            if(scope.autuado.ocorrenciaInfracao.perguntaTreze === true){
                                for(var i = 0; i < scope.autuado.documentos.length; i++){
                                    if(scope.autuado.documentos[i].tipoNotificacao === 'NOTIFICACAO_PARA_DESOCUPACAO_EM_90_DIAS'){
                                        scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected = true;
                                    }else{
                                        scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected = false;
                                    }
                                }
                            }

                            RelatorioInfracaoService.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});
                        }

                        scope.isBemDominial = true;
                    };

                    scope.enderecoCopy = angular.copy(scope.relatorioFiscalizacao.demanda.endereco);
                    scope.enderecoCopy.id = null;

                    scope.initAreasCopy = function(){
                        $rootScope.areasInfracao = {};
                        $rootScope.areasInfracao.areaConstrucao = null;
                        $rootScope.areasInfracao.areaObra = null;
                        $rootScope.areasInfracao.areaCercamento = null;
                        $rootScope.areasInfracao.areaAterro = null;
                        $rootScope.areasInfracao.areaEquipamento = null;
                        $rootScope.areasInfracao.areaEquipamento = null;
                        $rootScope.areasInfracao.areaDesmatamento = null;
                        $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                        $rootScope.areasInfracao.areaAutuada = null;
                    };

                    /**
                     * Regras referente ao tipo de  infração para o tipo de imóvel (BEM DE USO DOMINIAL)
                     * @param resp
                     */
                    scope.bemUsoDominialPerguntaUm = function (resp) {
                        if(typeof resp !== "undefined") {
                            !$rootScope.ocorrenciaInfracao ? $rootScope.ocorrenciaInfracao = {} : '';
                            $rootScope.ocorrenciaInfracao.perguntaUmCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaUm(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {
                                    scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy(resp);

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function () {
                                    });
                                });
                            });
                        }
                    };

                    scope.bemUsoDominialPerguntaDois = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaDoisCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaDois(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaDois(resp);

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaDois = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaTres = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaTresCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaTres(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaTres(resp);

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaTres = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaQuatro = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaQuatroCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaQuatro(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaQuatro(resp);
                                    scope.areaPerguntaQuatro(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaQuatro = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy(resp);
                    };

                    scope.areaPerguntaQuatro = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }else{
                            $rootScope.areasInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaCinco = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaCincoCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaCinco(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaCinco(resp);
                                    scope.areaPerguntaCinco(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaCinco = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy(resp);
                    };

                    scope.areaPerguntaCinco = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }else{
                            scope.autuado.ocorrenciaInfracao.perguntaQuatro === true ? scope.areasInfracao.areaObra = $rootScope.areasInfracao.areaObra : $rootScope.areasInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaSeis = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaSeisCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaSeis(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaSeis(resp);
                                    scope.areaPerguntaSeis(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao,function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaSeis = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy(resp);
                    };

                    scope.areaPerguntaSeis = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento ? scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento : scope.autuado.ocorrenciaInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;

                        }else{
                            scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                            $rootScope.areasInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaSete = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaSeteCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaSete(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaSete(resp);
                                    scope.areaPerguntaSete(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaSete = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy(resp);
                    };

                    scope.areaPerguntaSete = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento ? scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento : scope.autuado.ocorrenciaInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias ? scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias : scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }else{
                            scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                            scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaOito = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaOitoCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaOito(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaOito(resp);
                                    scope.areaPerguntaOito(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaOito = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy(resp);
                    };

                    scope.areaPerguntaOito = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;;
                            $rootScope.areasInfracao.areaEquipamento ? scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento : scope.autuado.ocorrenciaInfracao.areaEquipamento = null;;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias ? scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias : scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = null;;
                            $rootScope.areasInfracao.areaDesmatamento ? scope.autuado.ocorrenciaInfracao.areaDesmatamento = $rootScope.areasInfracao.areaDesmatamento : scope.autuado.ocorrenciaInfracao.areaDesmatamento = null;;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }else{
                            scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                            scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento;
                            scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias;
                            $rootScope.areasInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaNove = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaNoveCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaNove(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaNove(resp);
                                    scope.areaPerguntaNove(resp);

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaNove = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy(resp);
                    };

                    scope.areaPerguntaNove = function(resp){
                        if(resp === true){
                            $rootScope.areasInfracao.areaObra ? scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra : scope.autuado.ocorrenciaInfracao.areaObra = null;
                            $rootScope.areasInfracao.areaEquipamento ? scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento : scope.autuado.ocorrenciaInfracao.areaEquipamento = null;
                            $rootScope.areasInfracao.areaOutrasBenfeitorias ? scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias : scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = null;
                            $rootScope.areasInfracao.areaDesmatamento ? scope.autuado.ocorrenciaInfracao.areaDesmatamento = $rootScope.areasInfracao.areaDesmatamento : scope.autuado.ocorrenciaInfracao.areaDesmatamento = null;
                            $rootScope.areasInfracao.areaCercamento ? scope.autuado.ocorrenciaInfracao.areaCercamento = $rootScope.areasInfracao.areaCercamento : scope.autuado.ocorrenciaInfracao.areaCercamento = null;
                        }else{
                            scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                            scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento;
                            scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias;
                            scope.autuado.ocorrenciaInfracao.areaDesmatamento = $rootScope.areasInfracao.areaDesmatamento;
                            $rootScope.areasInfracao.areaCercamento = null;
                        }
                    };

                    scope.bemUsoDominialPerguntaDez = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaDezCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaDez(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaDez(resp);
                                    scope.areaFim();

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao, function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaDez = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy($rootScope.ocorrenciaInfracao.perguntaNoveCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDez = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaOnze = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaOnze(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaOnze(resp);
                                    scope.areaFim();

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao,function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaOnze = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy($rootScope.ocorrenciaInfracao.perguntaNoveCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDez = angular.copy($rootScope.ocorrenciaInfracao.perguntaDezCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOnze = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaDoze = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaDozeCopy = angular.copy(resp);

                            RelatorioInfracaoService.cleanPerguntaDoze(scope.relatorioFiscalizacao, function () {
                                RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                    scope.populaPerguntaDoze(resp);
                                    scope.areaFim();

                                    //Calcula o tamanho da área autuada com base nos campos preenchidos
                                    scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao,function(){});

                                    //Se necessário gera os documentos necessários para a questão do questionário
                                    scope.geraDocumentosDominial(function(){});
                                });
                            });
                        }
                    };

                    scope.populaPerguntaDoze = function(resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy($rootScope.ocorrenciaInfracao.perguntaNoveCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDez = angular.copy($rootScope.ocorrenciaInfracao.perguntaDezCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOnze = angular.copy($rootScope.ocorrenciaInfracao.perguntaOnzeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDoze = angular.copy(resp);
                    };

                    scope.bemUsoDominialPerguntaTreze = function (resp) {
                        if(typeof resp !== "undefined") {
                            $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = angular.copy(resp);

                            RelatorioInfracaoService.initInfracao(scope.autuado, function () {

                                scope.populaPerguntaTreze(resp);
                                scope.areaFim();

                                //Calcula o tamanho da área autuada com base nos campos preenchidos
                                scope.calculaAreaAutuada(scope.autuado.ocorrenciaInfracao,function(){});

                                //Se necessário gera os documentos necessários para a questão do questionário
                                scope.geraDocumentosDominial(function(){});
                            });
                        }
                    };

                    scope.populaPerguntaTreze = function (resp){
                        scope.autuado.ocorrenciaInfracao.perguntaUm = angular.copy($rootScope.ocorrenciaInfracao.perguntaUmCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDois = angular.copy($rootScope.ocorrenciaInfracao.perguntaDoisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTres = angular.copy($rootScope.ocorrenciaInfracao.perguntaTresCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaQuatro = angular.copy($rootScope.ocorrenciaInfracao.perguntaQuatroCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaCinco = angular.copy($rootScope.ocorrenciaInfracao.perguntaCincoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSeis = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeisCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaSete = angular.copy($rootScope.ocorrenciaInfracao.perguntaSeteCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOito = angular.copy($rootScope.ocorrenciaInfracao.perguntaOitoCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaNove = angular.copy($rootScope.ocorrenciaInfracao.perguntaNoveCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDez = angular.copy($rootScope.ocorrenciaInfracao.perguntaDezCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaOnze = angular.copy($rootScope.ocorrenciaInfracao.perguntaOnzeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaDoze = angular.copy($rootScope.ocorrenciaInfracao.perguntaDozeCopy);
                        scope.autuado.ocorrenciaInfracao.perguntaTreze = angular.copy(resp);
                    };

                    scope.areaFim = function(){
                        scope.autuado.ocorrenciaInfracao.areaObra = $rootScope.areasInfracao.areaObra;
                        scope.autuado.ocorrenciaInfracao.areaEquipamento = $rootScope.areasInfracao.areaEquipamento;
                        scope.autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = $rootScope.areasInfracao.areaOutrasBenfeitorias;
                        scope.autuado.ocorrenciaInfracao.areaDesmatamento = $rootScope.areasInfracao.areaDesmatamento;
                        scope.autuado.ocorrenciaInfracao.areaCercamento = $rootScope.areasInfracao.areaCercamento;
                    };

                    /**
                     *  Gera os documentos necessários para o tipo de imóvel (BEM DOMINIAL)
                     */
                    scope.geraDocumentosDominial = function () {
                        scope.autuado.documentos = [];

                        var documento = {};

                        if (scope.autuado.ocorrenciaInfracao.perguntaUm !== null && scope.autuado.ocorrenciaInfracao.perguntaUm === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao = 'NOTIFICACAO_PARA_COMPLEMENTACAO_DE_DOCUMENTACAO';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaDois !== null && scope.autuado.ocorrenciaInfracao.perguntaDois === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao = 'NOTIFICACAO_PARA_COMPLEMENTACAO_DE_DOCUMENTACAO';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaTres !== null && scope.autuado.ocorrenciaInfracao.perguntaTres === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao = 'NOTIFICACAO_PARA_COMPLEMENTACAO_DE_DOCUMENTACAO';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaQuatro !== null && scope.autuado.ocorrenciaInfracao.perguntaQuatro === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.embargo = true;
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.sancaoAdministrativa.desocupacao = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }

                                if (!scope.autuado.existeEmbargo) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_EMBARGO';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaCinco !== null && scope.autuado.ocorrenciaInfracao.perguntaCinco === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.sancaoAdministrativa.desocupacao = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaSeis !== null && scope.autuado.ocorrenciaInfracao.perguntaSeis === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.sancaoAdministrativa.desocupacao = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaSete !== null && scope.autuado.ocorrenciaInfracao.perguntaSete === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.sancaoAdministrativa.desocupacao = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaOito !== null && scope.autuado.ocorrenciaInfracao.perguntaOito === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaNove !== null && scope.autuado.ocorrenciaInfracao.perguntaNove === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeInfracao) {
                                    documento = {};
                                    documento.tipoDocumento = 'AUTO_DE_INFRACAO';
                                    documento.sancaoAdministrativa = {};
                                    documento.sancaoAdministrativa.multa = true;
                                    documento.sancaoAdministrativa.desocupacao = true;
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaDez !== null && scope.autuado.ocorrenciaInfracao.perguntaDez === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_30_DIAS';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaOnze !== null && scope.autuado.ocorrenciaInfracao.perguntaOnze === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao  = 'NOTIFICACAO_PARA_DESOCUPACAO_IMEDIATA';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaDoze !== null && scope.autuado.ocorrenciaInfracao.perguntaDoze === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.tipoNotificacao  = 'NOTIFICACAO_PARA_DESOCUPACAO_IMEDIATA';
                                    documento.endereco = scope.enderecoCopy;
                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }

                        if (scope.autuado.ocorrenciaInfracao.perguntaTreze !== null && scope.autuado.ocorrenciaInfracao.perguntaTreze === true) {
                            RelatorioInfracaoService.verificaDocumentos(scope.autuado, function () {
                                if (!scope.autuado.existeNotificacao) {
                                    documento = {};
                                    documento.tipoDocumento = 'NOTIFICACAO';
                                    documento.endereco = scope.enderecoCopy;

                                    if(scope.autuado.ocorrenciaInfracao.tipoNotificacaoSelected === true){
                                        documento.tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_90_DIAS';
                                    }else{
                                        documento.tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_180_DIAS';
                                    }

                                    scope.autuado.documentos.push(angular.copy(documento));
                                }
                            });
                        }
                    };

                    scope.handleTipoNotificacaoSelected = function(resp){
                      for(var i = 0; i < scope.autuado.documentos.length; i++){
                          if(scope.autuado.documentos[i].tipoDocumento === 'NOTIFICACAO'){
                              if(resp === false){
                                  scope.autuado.documentos[i].tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_180_DIAS';
                              }else{
                                  scope.autuado.documentos[i].tipoNotificacao = 'NOTIFICACAO_PARA_DESOCUPACAO_EM_90_DIAS';
                              }
                          }
                      }
                    };

                    /**
                     * Prepara o documento para a sua visualização
                     * @param documento
                     * @param event
                     */
                    scope.visualizarDocumento = function (documento, event) {
                        documento.apenasLeitura = true;

                        RelatorioInfracaoService.openModal(event, documento, scope.relatorioFiscalizacao, scope.autuado.responsavel, scope.relatorioFiscalizacao.inicio, scope);
                    };

                    /**
                     * Prepara o documento para a sua edição
                     * @param documento
                     * @param index
                     * @param ev
                     */
                    scope.updateDocumentoDominial = function (documento, index, ev) {
                        if(documento.id){
                            documento = documento;
                            documento.index = index;
                            documento.isUpdate = true;
                            documento.apenasLeitura = false;

                            RelatorioInfracaoService.openModalJustificativa(ev, documento, scope.relatorioFiscalizacao, scope.autuado, scope.relatorioFiscalizacao.inicio, 'edicao', scope);
                        }else{
                            RelatorioInfracaoService.openModal(event, documento, scope.relatorioFiscalizacao, scope.autuado.responsavel, scope.relatorioFiscalizacao.inicio, scope);
                        }
                    };

                    /**
                     * Prepara o documento para a sua edição
                     * @param documento
                     * @param index
                     * @param ev
                     */
                    scope.excluirDocumentoDominial = function(documento, index, ev) {
                        documento = documento;
                        documento.index = index;
                        documento.isUpdate = true;
                        documento.apenasLeitura = false;

                        RelatorioInfracaoService.openModalJustificativa(ev, documento, scope.relatorioFiscalizacao, scope.autuado, scope.relatorioFiscalizacao.inicio, 'excluir', scope);
                    };

                    scope.calculaAreaAutuada = function (ocorrenciaInfracao, callback) {
                        !$rootScope.areasInfracao ? $rootScope.areasInfracao = {} : "";

                        $rootScope.areasInfracao.areaConstrucao = angular.copy(ocorrenciaInfracao.areaConstrucao);
                        $rootScope.areasInfracao.areaObra = angular.copy(ocorrenciaInfracao.areaObra);
                        $rootScope.areasInfracao.areaCercamento = angular.copy(ocorrenciaInfracao.areaCercamento);
                        $rootScope.areasInfracao.areaAterro = angular.copy(ocorrenciaInfracao.areaAterro);
                        $rootScope.areasInfracao.areaEquipamento = angular.copy(ocorrenciaInfracao.areaEquipamento);
                        $rootScope.areasInfracao.areaDesmatamento = angular.copy(ocorrenciaInfracao.areaDesmatamento);
                        $rootScope.areasInfracao.areaOutrasBenfeitorias = angular.copy(ocorrenciaInfracao.areaOutrasBenfeitorias);

                        RelatorioInfracaoService.calculaAreaAutuada(ocorrenciaInfracao, function () {

                            if (callback) {
                                callback();
                            }
                        });
                    };

                    scope.init();
                });
            }
        }
    }
})();

(function () {
    'use strict';
    /*global angular*/
    angular.module('ciMapaDirectiveFiscalizacao', []);
}());
(function () {
  'use strict';
  angular
    .module('ciMapaDirectiveFiscalizacao')
    .factory('olUtils', function () {
      var duration = 2000;

      return {
        zoomByExtent: zoomByExtent,
        zoomByExtentNoAnimation: zoomByExtentNoAnimation,
        extent2array: extent2array,
        panAnimation: panAnimation,
        getCenterByExtent: getCenterByExtent,
        getResolutionForExtent: getResolutionForExtent,
        zoomAnimation: zoomAnimation,
        extentFeaturesExtent: extentFeaturesExtent,
        wkt2feature: wkt2feature,
        desenhaArquivo: desenhaArquivo
      };

      function zoomByExtentNoAnimation(map, extent) {
        extent = extent2array(extent);
        if (extent) {
          var center = getCenterByExtent(extent);
          panAnimation(map, center);
          var resolution = getResolutionForExtent(extent, map.getSize());
          zoomAnimation(map, resolution);
        }
      }

      function zoomByExtent(map, pExtent) {
        var extent = extent2array(pExtent);
        if (extent) {
		  var center = getCenterByExtent(extent);
		  map.getView().setCenter(ol.proj.fromLonLat(center));
		  //var resolution = getResolutionForExtent(extent, map.getSize());
          //map.getView().setResolution(resolution);

        }
      }

      function extent2array(extent) {
        if (typeof extent === 'string') {
          if (extent.substring(0, 3) === 'BOX') {
            extent = extent.replace('BOX(', '').replace(')', '').replace(',', ' ').split(' ');
          } else if (extent.substring(0, 3) === 'Env') {
            extent = extent.replace('Env[', '').replace(']', '').replace(/ /g, '').replace(/:/g, ' ').replace(/,/, ' ').split(' ');
            extent = [extent[0], extent[2], extent[1], extent[3]];
          }
        }

        if (extent instanceof Array) {
          for (var i = 0; i < extent.length; i++) {
            extent[i] = parseFloat(extent[i]);
          }
        }

        return extent;
      }

      function getCenterByExtent(bbox) {
        return ol.extent.getCenter(bbox);
      }

      function panAnimation(map, center) {
        var start = Number(new Date());
        var pan = ol.animation.pan({
          duration: duration,
          source: (map.getView().getCenter()),
          start: start
        });
        map.beforeRender(pan);
        map.getView().setCenter(center);
      }

      function getResolutionForExtent(extent, size) {
        var xResolution = ol.extent.getWidth(extent) / size[0];
        var yResolution = ol.extent.getHeight(extent) / size[1];
        return Math.max(xResolution, yResolution);
      }

      function zoomAnimation(map, newResolution) {
        var start = Number(new Date());
        var zoom = ol.animation.zoom({
          duration: duration,
          resolution: map.getView().getResolution(),
          start: start
        });
        map.beforeRender(zoom);
        map.getView().setResolution(newResolution);
      }

      function extentFeaturesExtent(extents) {
        var coordenadas = [];
        extents.forEach(function (extent) {
          extent = extent2array(extent);
          var ex = extent;
          var c1 = [ex[0], ex[1]];
          var c2 = [ex[2], ex[3]];
          coordenadas.push(c1);
          coordenadas.push(c2);
        });
        return ol.extent.boundingExtent(coordenadas);
      }

      function wkt2feature(geomWkt) {
        var format = new ol.format.WKT();
        return format.readFeature(geomWkt);
      }

      function desenhaArquivo(map, geom) {
        var format = new ol.format.WKT();
        var feature = format.readFeature(geom);
        var vector = new ol.layer.Vector({
          source: new ol.source.Vector({
            features: [feature]
          })
        });
        map.addLayer('draw', vector);
        this.zoomByExtent(feature.getGeometry().getExtent());
      }
    });
})();

(function () {
  'use strict';
  angular
    .module('ciMapaDirectiveFiscalizacao').factory('giOlFunctions', function () {
      var duration = 2000;

      return {
        extent2array: extent2array,
        extent2arrayENV: extent2arrayENV,
        panAnimation: panAnimation,
        getCenterByExtent: getCenterByExtent,
        getResolutionForExtent: getResolutionForExtent,
        zoomAnimation: zoomAnimation,
        extentFeaturesExtent: extentFeaturesExtent,
        desenhaArquivo: desenhaArquivo,
        estiloRachurado: getStackedStyle,
        estiloRachuradoVermelho: getStackedStyleVermelho
      };

      function extent2array() {
        var array_extent = txt.replace('BOX(', '').replace(')', '').replace(',', ' ').split(' ');
        for (var i in array_extent) {
          array_extent[i] = parseFloat(array_extent[i]);
        }
        return array_extent;
      }

      function extent2arrayENV(txt) {
        var array_extent = txt.replace('Env[', '').replace(']', '').replace(/ /g, '').replace(/:/g, ' ').replace(/,/, ' ').split(' ');

        var array_final = [];
        for (var i in array_extent) {
          if (i % 2 == 0) {
            array_final.push(parseFloat(array_extent[i]));
          }
        }
        for (var i in array_extent) {
          if (i % 2 == 1) {
            array_final.push(parseFloat(array_extent[i]));
          }
        }
        return array_final;
      }

      function getCenterByExtent(bbox) {
        return ol.extent.getCenter(bbox);
      }

      function panAnimation(map, center) {
        var start = Number(new Date());
        var pan = ol.animation.pan({
          duration: duration,
          source: (map.getView().getCenter()),
          start: start
        });
        map.beforeRender(pan);
        map.getView().setCenter(center);
      }

      function getResolutionForExtent(extent, size) {
        var xResolution = ol.extent.getWidth(extent) / size[0];
        var yResolution = ol.extent.getHeight(extent) / size[1];
        return Math.max(xResolution, yResolution);
      }

      function getCenterByExtent(bbox) {
        return ol.extent.getCenter(bbox);
      }

      function zoomAnimation(map, newResolution) {
        var start = Number(new Date());
        var zoom = ol.animation.zoom({
          duration: duration,
          resolution: map.getView().getResolution(),
          start: start
        });
        map.beforeRender(zoom);
        map.getView().setResolution(newResolution);
      }

      function createStylecluster(feature, size, color, icon, width, color_stroke) {
        var raioCluster;
        if (size >= 1000) {
          raioCluster = 17;
        } else if (size < 1000 && size >= 100) {
          raioCluster = 14;
        } else {
          raioCluster = 11;
        }

        if (!width) {
          width = 1;
        }

        if (!color_stroke) {
          color_stroke = '#fff';
        }

        if (icon && size == 1) {
          style = [icon];
        } else {
          style = [new ol.style.Style({
            image: new ol.style.Circle({
              radius: raioCluster,
              stroke: new ol.style.Stroke({
                color: color_stroke,
                width: width
              }),
              fill: new ol.style.Fill({
                color: color
              })
            }),
            text: new ol.style.Text({
              text: size.toString(),
              fill: new ol.style.Fill({
                color: '#fff'
              })
            })
          })];
        }
        return style;
      }

      var iconStyle = new ol.style.Style({
        image: new ol.style.Icon(/** @type {olx.style.IconOptions} */ ({
          anchor: [8, 20],
          anchorXUnits: 'pixels',
          anchorYUnits: 'pixels',
          opacity: 1.0,
          src: 'static/imgs/icon_roxo.png'
        }))
      });

      var iconStyleSearch = new ol.style.Style({
        image: new ol.style.Icon(/** @type {olx.style.IconOptions} */ ({
          anchor: [8, 20],
          anchorXUnits: 'pixels',
          anchorYUnits: 'pixels',
          opacity: 1.0,
          src: 'static/imgs/icon_laranja_maior.png'
        }))
      });

      function createStylecluster(feature, size, color, icon, width, color_stroke) {
        var raioCluster;
        if (size >= 1000) {
          raioCluster = 17;
        } else if (size < 1000 && size >= 100) {
          raioCluster = 14;
        } else {
          raioCluster = 11;
        }

        if (!width) {
          width = 1;
        }

        if (!color_stroke) {
          color_stroke = '#fff';
        }

        if (icon && size == 1) {
          style = [icon];
        } else {
          style = [new ol.style.Style({
            image: new ol.style.Circle({
              radius: raioCluster,
              stroke: new ol.style.Stroke({
                color: color_stroke,
                width: width
              }),
              fill: new ol.style.Fill({
                color: color
              })
            }),
            text: new ol.style.Text({
              text: size.toString(),
              fill: new ol.style.Fill({
                color: '#fff'
              })
            })
          })];
        }
        return style;
      }

      var styleCacheCluster = {};
      function styleClusterImoveis(feature, resolution) {
        var size = feature.get('features').length;
        var style = styleCacheCluster[size];
        if (!style) {
          style = createStylecluster(feature, size, '#410047', iconStyle);
          styleCacheCluster[size] = style;
        }
        return style;
      }

      var styleCacheSearch = {};
      function styleClusterSearch(feature, resolution) {
        var size = feature.get('features').length;
        var style = styleCacheSearch[size];
        if (!style) {
          style = createStylecluster(feature, size, '#FF6600', iconStyleSearch);
          styleCacheSearch[size] = style;
        }
        return style;
      }

      var styleCacheClusterUF = {};
      function styleClusterUF(feature, resolution) {
        var features = feature.get('features');
        var size = 0;
        var color = '#0303d7';
        for (var i in features) {
          var fea = features[i];
          if (fea.getProperties) {
            var p = fea.getProperties();
            size += p.qtd;
            color = colorsCluster[p.geocodigo_uf];
          }
        }
        var style = styleCacheClusterUF[color + '-' + size];
        if (!style) {
          style = createStylecluster(feature, size, color);
          styleCacheClusterUF[color + '-' + size] = style;
        }
        return style;
      }

      function createCluster(data, distance, style) {
        var geojsonFormat = new ol.format.GeoJSON();
        var features = geojsonFormat.readFeatures(data);

        var source = new ol.source.Vector();
        source.addFeatures(features);

        var clusterSource = new ol.source.Cluster({
          distance: distance,
          source: source
        });

        return new ol.layer.Vector({
          source: clusterSource,
          style: style
        });
      }

      function getFeatureByProp(source, prop, val) {
        var features = source.getFeatures();
        for (var i in features) {
          var f = features[i];
          if (f.get(prop) == val) {
            return f;
          }
        }
      }

      var colorsCluster = {
        // Norte
        11: 'rgba(4, 74, 37, 0.8)',
        12: 'rgba(4, 74, 37, 0.8)',
        13: 'rgba(4, 74, 37, 0.8)',
        14: 'rgba(4, 74, 37, 0.8)',
        15: 'rgba(4, 74, 37, 0.8)',
        16: 'rgba(4, 74, 37, 0.8)',
        17: 'rgba(4, 74, 37, 0.8)',

        // Nordeste
        21: 'rgba(0, 0, 255, 0.8)',
        22: 'rgba(0, 0, 255, 0.8)',
        23: 'rgba(0, 0, 255, 0.8)',
        24: 'rgba(0, 0, 255, 0.8)',
        25: 'rgba(0, 0, 255, 0.8)',
        26: 'rgba(0, 0, 255, 0.8)',
        27: 'rgba(0, 0, 255, 0.8)',
        28: 'rgba(0, 0, 255, 0.8)',
        29: 'rgba(0, 0, 255, 0.8)',

        // Sudeste
        31: 'rgba(100, 100, 100, 0.8)',
        32: 'rgba(100, 100, 100, 0.8)',
        33: 'rgba(100, 100, 100, 0.8)',
        34: 'rgba(100, 100, 100, 0.8)',
        35: 'rgba(100, 100, 100, 0.8)',

        // Centro-Oeste
        50: 'rgba(233, 145, 0, 0.8)',
        51: 'rgba(233, 145, 0, 0.8)',
        52: 'rgba(233, 145, 0, 0.8)',
        53: 'rgba(233, 145, 0, 0.8)',

        // Sul
        41: 'rgba(255, 0, 0, 0.8)',
        42: 'rgba(255, 0, 0, 0.8)',
        43: 'rgba(255, 0, 0, 0.8)'
      };

      var styleCacheOverlay = {};
      function styleOverlay(feature, resolution) {
        var features = feature.get('features');

        var size = 0;
        var color = '#410047';
        var icon = iconStyle;

        var layer_name = getFeatureLayerName(feature);
        if (layer_name == 'imovel' || layer_name.indexOf('imovel_busca') != -1 || layer_name == 'imovel_venda' || layer_name == 'imovel' || layer_name == 'imovel_busca_venda') {
          var size = feature.get('features').length;
          if (layer_name.indexOf('imovel_busca') != -1 || layer_name == 'imovel_busca_venda') {
            color = '#FF6600';
            icon = iconStyleSearch;
          }
        } else if (layer_name = 'uf_point' || layer_name == 'municipio_point') {
          icon = null;
          for (var i in features) {
            var fea = features[i];
            size += fea.get('qtd');
            color = colorsCluster[fea.get('geocodigo_uf')];
          }
        }

        var style = styleCacheOverlay[color + '-' + size];
        if (!style) {
          style = createStylecluster(feature, size, color, icon, 2, '#FFF');
          styleCacheOverlay[color + '-' + size] = style;
        }

        return style;
      }

      function desenhaArquivo(geom) {
        var format = new ol.format.WKT();
        var feature = format.readFeature(geom);
        vector = new ol.layer.Vector({
          source: new ol.source.Vector({
            features: [feature]
          })
        });
        viewer.addLayer('draw', vector);
        viewer.setZoom(feature.getGeometry().getExtent());
      }

      function extentFeaturesExtent(extents) {
        var coordenadas = [];
        extents.forEach(function (extent) {
          if (typeof extent === 'string') {
            var substring = extent;
            if (substring.substring(0, 3) == 'BOX') {
              extent = extent2array(extent);
            }
            if (substring.substring(0, 3) == 'Env') {
              extent = extent2arrayENV(extent);
            }
          }
          var ex = extent;
          var c1 = [ex[0], ex[1]];
          var c2 = [ex[2], ex[3]];
          coordenadas.push(c1);
          coordenadas.push(c2);
        });
        return ol.extent.boundingExtent(coordenadas);
      }
    });

      // estilo rachurado
  var canvas = document.createElement('canvas');
  var pixelRatio = ol.has.DEVICE_PIXEL_RATIO;
  var context = canvas.getContext('2d');
  var pattern = (function () {
    canvas.width = 11 * pixelRatio;
    canvas.height = 11 * pixelRatio;
    context.moveTo(0, 0);
    context.lineTo(300, 150);
    context.stroke();
    return context.createPattern(canvas, 'repeat');
  })();

  var fill = new ol.style.Fill();
  var style = new ol.style.Style({
    fill: fill,
    stroke: new ol.style.Stroke({
      color: '#333',
      width: 2
    })
  });

  var styleBordaVermelha = new ol.style.Style({
    fill: fill,
    stroke: new ol.style.Stroke({
      color: '#D31010',
      width: 2
    })
  });

  var getStackedStyle = function (feature, resolution) {
    var id = 1;
    fill.setColor(id > 'J' ? gradient(feature, resolution) : pattern);
    return style;
  };

  var getStackedStyleVermelho = function (feature, resolution) {
    var id = 1;
    fill.setColor(id > 'J' ? gradient(feature, resolution) : pattern);
    return styleBordaVermelha;
  };
})();

(function(){
      angular.module('ciMapaDirectiveFiscalizacao')
      .factory('NotificadorServiceFiscalizacao', ["$rootScope", function($rootScope) {

    return {
        subscribe: function(scope, callback, eventName) {
            var handler = $rootScope.$on(eventName, callback);
            scope.$on('$destroy', handler);
        },

        notify: function(scope,object, eventName) {
            $rootScope.$emit(eventName,scope,object);
        }
    };
}]);

})();

(function () {
    'use strict';
    /*global angular*/
angular.module("ciMapaDirectiveFiscalizacao")
    .factory("PontosServiceFiscalizacao", ["$resource", function($resource){
        var urlBase = "ciMapa";
        return $resource(urlBase + "imovel/pontos");
    }])
    .factory("TerrenoCadastralServiceFiscalizacao", ["$resource", function($resource){
        var urlBase = "asdasd";
        return $resource(urlBase + 'cartografia/terrenos-cadastrais');
    }])
    .factory("TerrenoCartorialServiceFiscalizacao", ["$resource", function($resource){
        var urlBase = "asdasd";
        return $resource(urlBase + 'cartografia/terrenos-cartoriais');
    }])
    .factory("TerrenoBenfeitoriaServiceFiscalizacao", ["$resource", function($resource){
        var urlBase = "asdasd";
        return $resource(urlBase + 'cartografia/benfeitorias');
    }])
    .factory("PontosUnidadeAutonomaServiceFiscalizacao", ["$resource", function($resource){
        var urlBase = "asdasd";
        return $resource(urlBase + "cartografia/pontos-unidade-autonoma");
    }]);
}());

(function() {
    'use strict';
    angular
        .module('ciMapaDirectiveFiscalizacao')
        .factory('ciMapaServiceFiscalizacao', ["$http", function($http) {
            var nominatimApi = 'https://nominatim.openstreetmap.org';

            var buscarLocalidadesPorCoordenadas = function(busca) {
                return $http.get(nominatimApi + '/search?q=' + busca +
                    '&format=json&countrycodes=br&limit=5&polygon_geojson=1'
                );
            };

            return {
                buscarLocalidadesPorCoordenadas: buscarLocalidadesPorCoordenadas,
            };
        }]);
})();
(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('ciMapaDirectiveFiscalizacao')
    .factory('ciMapaViewFactoryFiscalizacao', ["MAPA_CONSTANTS_FISCALIZACAO", function (MAPA_CONSTANTS_FISCALIZACAO) {

        var view, init;

        init = function () {

            view = new ol.View({
                center: ol.proj.fromLonLat(MAPA_CONSTANTS_FISCALIZACAO.GEOMETRY.GEOMETRIA_INICIAL),
                zoom: 4,
                maxZoom: 19,
                minZoom: 1
            });
        };

        init();

        return {
            getView: function () {
                return view;
            },
            getView2: function (ponto) {
                view = new ol.View({
                    center: ol.proj.fromLonLat(ponto, 'EPSG:4326'),
                    zoom: 18,
                    maxZoom: 19,
                    minZoom: 1
                });
                return view;
            },
            getView3: function (ponto) {
                view = new ol.View({
                    center: ol.proj.fromLonLat(ponto, 'EPSG:3857'),
                    zoom: 4.3
                });
                return view;
            },
            centralizarMapa: function (geometry, size) {
                view.fit(geometry, size);
            },

            init: init
        };
    }]);
}());

(function () {
    "use strict";
    /*global ol*/
    angular.module("ciMapaDirectiveFiscalizacao").
    factory("ciMapaStyleFactoryFiscalizacao", function (){

        function PinCinzaUA() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-cinza-32-UA.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                })
            });
        }

        function PinCinza() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    src: 'assets/img/icons/pin-cinza-32.png'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                })
            });
        }

        function PinAmarelo() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    crossOrigin: 'anonymous',
                    src: 'assets/img/icons/pin.png',
                    color: '#FFFF00'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function PinVermelho() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    crossOrigin: 'anonymous',
                    src: 'assets/img/icons/pin.png',
                    color: '#FF0000'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }



        function PinVerde() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    crossOrigin: 'anonymous',
                    src: 'assets/img/icons/pin.png',
                    color: '#14ec00'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function PinAzul() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    crossOrigin: 'anonymous',
                    src: 'assets/img/icons/pin.png',
                    color: '#1E90FF'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }

        function TerrenoCinza() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(171, 171, 171, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: 'rgba(171, 171, 171, 1)',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoCinzaEscuro() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(100, 100, 100, 0.5)'
                }),
                stroke: new ol.style.Stroke({
                    color: 'rgba(120, 120, 120, 1)',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoAmarelo() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(255, 222, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#ffde00',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoAmareloPontilhado() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(255, 222, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#ffde00',
                    width: 3,
                    lineDash: [4]
                }),
                zIndex: 1
            });
        }

        function TerrenoVerde() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(20, 236, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#14ec00',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoVerdePontilhado() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(20, 236, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#14ec00',
                    width: 3,
                    lineDash: [4]
                }),
                zIndex: 1
            });
        }

        function TerrenoVermelho() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: 'rgba(255, 0, 0, 0.3)'
                }),
                stroke: new ol.style.Stroke({
                    color: '#ff0000',
                    width: 3
                }),
                zIndex: 1
            });
        }

        function TerrenoRoxo() {
            return new ol.style.Style({
                fill: new ol.style.Fill({
                    color: '#6c009e'
                }),
                stroke: new ol.style.Stroke({
                    color: '#6c009e'
                }),
                zIndex: 1
            });
        }

        function CirculoCinza() {
            return new ol.style.Style({
                image: new ol.style.Circle({
                    radius: 15,
                    stroke: new ol.style.Stroke({
                        color: '#fff'
                    }),
                    fill: new ol.style.Fill({
                        color: '#777777'
                    })
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    })
                })
            });
        }

        function TrianguloVerde() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    crossOrigin: 'anonymous',
                    src: 'assets/img/icons/triangulo-30.png',
                    color: '#14ec00'
                }),
                text: new ol.style.Text({
                    font: "bold 12px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }
        function TrianguloCinza() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    crossOrigin: 'anonymous',
                    src: 'assets/img/icons/triangulo-30.png',
                    color: '#777777'
                }),
                text: new ol.style.Text({
                    font: "bold 12px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    })
                })
            });
        }


        function EstrelaAzul() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    crossOrigin: 'anonymous',
                    src: 'assets/img/icons/estrela.png',
                    color: '#1E90FF'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }
        function EstrelaAmarela() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    crossOrigin: 'anonymous',
                    src: 'assets/img/icons/estrela.png',
                    color: '#FFFF00'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }
        function EstrelaVermelha() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    crossOrigin: 'anonymous',
                    src: 'assets/img/icons/estrela.png',
                    color: '#FF0000'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }
        function EstrelaVerde() {
            return new ol.style.Style({
                image: new ol.style.Icon({
                    anchor: [0.5, 32],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'pixels',
                    crossOrigin: 'anonymous',
                    src: 'assets/img/icons/estrela.png',
                    color: '#14ec00'
                }),
                text: new ol.style.Text({
                    font: "bold 14px sans-serif",
                    fill: new ol.style.Fill({
                        color: '#fff'
                    }),
                    stroke: new ol.style.Stroke({
                        color: "#000"
                    }),
                    offsetY: -22
                }),
                zIndex: 6
            });
        }


        return {
            pinAzul: function () {
                return new PinAzul();
            },
            pinAmarelo: function () {
                return new PinAmarelo();
            },
            pinVermelho: function () {
                return new PinVermelho();
            },
            pinVerde: function () {
                return new PinVerde();
            },
            terrenoVerde: function () {
                return new TerrenoVerde();
            },
            estrelaAzul: function () {
                return new EstrelaAzul();
            },
            estrelaAmarela: function () {
                return new EstrelaAmarela();
            },
            estrelaVerde: function () {
                return new EstrelaVerde();
            },
            estrelaVermelha: function () {
                return new EstrelaVermelha();
            },
            trianguloCinza: function() {
                return new TrianguloCinza();
            },
            trianguloVerde: function(){
                return new TrianguloVerde();
            }
        }
    });
})();

(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('ciMapaDirectiveFiscalizacao')
    .factory('ciMapaLayersFactoryFiscalizacao', function () {

        var vm = this,
            init,
            camadasMapa,
            camadasMinimapa,
            osm,
            bingMapa,
            bingMinimapa;

        init = function () {
            camadasMapa = new ol.Collection();
            camadasMinimapa = new ol.Collection();

            vm.setOsm();
            vm.setBingMapa();
            vm.setBingMinimapa();
        };


        vm.setOsm = function () {
            osm = new ol.layer.Tile({
                source: new ol.source.OSM(),
                name: 'osm',
                required: true
            });
            camadasMapa.push(osm);
            camadasMinimapa.push(osm);
        };

        vm.setBingMapa = function () {
            bingMapa =  new ol.layer.Tile({
                source: new ol.source.BingMaps({
                    key: 'AtwyeZCHS5j5qQT3JNFayXjp_8FvRtPWefOsSW5g6MFzMgPIRvzLt5BhfsDnppQ9',
                    imagerySet: 'AerialWithLabels'
                }),
                visible: false,
                name: 'bingMapa',
                required: true
            });
            camadasMapa.push(bingMapa);
        };

        vm.setBingMinimapa = function () {
            bingMinimapa =  new ol.layer.Tile({
                source: new ol.source.BingMaps({
                    key: 'AtwyeZCHS5j5qQT3JNFayXjp_8FvRtPWefOsSW5g6MFzMgPIRvzLt5BhfsDnppQ9',
                    imagerySet: 'AerialWithLabels'
                }),
                visible: true,
                name: 'bingMinimapa',
                required: true
            });
            camadasMinimapa.push(bingMinimapa);
        };

        return {

            getCamadas: function (isMinimapa) {
                if (isMinimapa) {
                    return camadasMinimapa;
                } else {
                    return camadasMapa;
                }
            },

            init: init
        };
    });
}());

(function () {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('ciMapaDirectiveFiscalizacao')
    .directive('ciMinimapaFiscalizacao', ["ciMapaViewFactoryFiscalizacao", "ciMapaLayersFactoryFiscalizacao", "NotificadorServiceFiscalizacao", "MAPA_CONSTANTS_FISCALIZACAO", function (ciMapaViewFactoryFiscalizacao, ciMapaLayersFactoryFiscalizacao, NotificadorServiceFiscalizacao, MAPA_CONSTANTS_FISCALIZACAO) {
        return {
            scope: {
            },
            template: '<div></div>',
            link: function (scope, element, attrs, ctrl) {
                angular.element(document).ready(function () {
                    var viewFac = ciMapaViewFactoryFiscalizacao,
                        layersFac = ciMapaLayersFactoryFiscalizacao,
                        layers = layersFac.getCamadas(true),
                        map,
                        notifiers = MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES,
                        height,
                        width;

                    if (attrs.height && !isNaN(attrs.height)) {
                        height = parseInt(attrs.height, 10);
                    } else {
                        height = 100;
                    }
                    if (attrs.width && !isNaN(attrs.width)) {
                        width = parseInt(attrs.width, 10);
                    } else {
                        width = 100;
                    }

                    map = new ol.Map({
                        target: element[0].firstChild,
                        layers: layers,
                        view: viewFac.getView(),
                        controls: ol.control.defaults({
                            attribution: false,
                            zoom: false,
                            rotate: false
                        }),
                        interactions: ol.interaction.defaults({
                            mouseWheelZoom: false,
                            doubleClickZoom: false,
                            altShiftDragRotate: false,
                            shiftDragZoom: false,
                            dragPan: false
                        })
                    });

                    map.setSize([width, height]);

                    NotificadorServiceFiscalizacao.subscribe(scope, function (event, scope, boolean) {
                        angular.forEach(map.getLayers(), function (value) {
                            if (value.get('name') === 'bingMinimapa') {
                                value.setVisible(!boolean);
                            }
                        });
                    }, notifiers.ALTERAR_VISIBILIDADE_BING);
                });
            }
        };
    }]);
}());

(function() {
    'use strict';
    /*global angular*/
    /*global ol*/
    angular.module('ciMapaDirectiveFiscalizacao')
    .directive('ciMapaFiscalizacao', ["ciMapaViewFactoryFiscalizacao", "ciMapaLayersFactoryFiscalizacao", "NotificadorServiceFiscalizacao", "MAPA_CONSTANTS_FISCALIZACAO", "$timeout", "$state", "ciMapaStyleFactoryFiscalizacao", "ComumFactory", "giOlFunctions", "olUtils", function(ciMapaViewFactoryFiscalizacao, ciMapaLayersFactoryFiscalizacao, NotificadorServiceFiscalizacao, MAPA_CONSTANTS_FISCALIZACAO, $timeout, $state, ciMapaStyleFactoryFiscalizacao, ComumFactory, giOlFunctions, olUtils) {
        return {
            templateUrl: 'scripts-fiscalizacao/app/directives/ci-mapaFiscalizacao/view/ci-mapa-template.html',
            scope: {
                showMinimapa: '=showminimapa',
                heightMinimapa: '=heightminimapa',
                widthMinimapa: '=widthminimapa',
                tabIndex: '=tabindex',
                action: '@',
                coord: '=',
                latitude: '=',
                longitude: '=',
                validar: '&',
                validarPoligono: '&',
                habilitarDesenho: '=',
                exportarMapa: '&',
                btLimpar: '=',
                btExportar: '=',
                btDesenhar: '=',
                btMarcar: '=',
                croqui: '=',
                demandas: '=',
                tipo: '=',
                visualizar: '=',
                imgMapa: '=',
                exportarImagem: '=',
                legenda: '=',
                estados: '=',
                apenasLeitura: '='
            },
            link: function(scope, element) {
                angular.element(document).ready(function() {
                    var vm = scope,
                        init,
                        initHeatMap,
                        initColorMap,
                        viewFac = ciMapaViewFactoryFiscalizacao,
                        layersFac = ciMapaLayersFactoryFiscalizacao,
                        imovelSource = new ol.source.Vector(),
                        imovelLayer = new ol.layer.Vector({
                            source: imovelSource,
                            name: 'imovel',
                            required: true
                        }),
                        geoJson = new ol.format.GeoJSON({
                            defaultProjection: 'EPSG:4674'
                        }),
                        drawPoint,
                        drawPolygon,
                        erase,
                        removerCustomInteractions,
                        setDrawPoint,
                        styles = ciMapaStyleFactoryFiscalizacao,
                        olFunctions = giOlFunctions,
                        olMapUtils = olUtils,
                        notifiers = MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES,
                        featureIds = MAPA_CONSTANTS_FISCALIZACAO.FEATURE_ID,
                        drawPolygonBenfeitoria,
                        geometriaImovelPonto,
                        feature = new ol.Feature({}),
                        drawPolygonUnidadeAutonoma,
                        pontoImovel,
                        benfeitoriaStyle,
                        imovel = {},
                        setFeature,
                        setHeatFeature,
                        setDrawPolygonBenfeitoria,
                        demanda;

                    vm.layers = layersFac.getCamadas(false);
                    vm.map = {};
                    vm.listaLayers = false;
                    vm.bloquearMapa = true;

                    init = function() {
                        if (vm.latitude && vm.longitude) {
                            imovel.coordinates = [vm.latitude, vm.longitude];
                            imovel.type = 'Point';
                        }

                        pontoImovel = styles.pinVerde();

                        benfeitoriaStyle = styles.terrenoVerde();

                        var controles = ol.control.defaults().extend([
                            new ol.control.ScaleLine(),
                            new ol.control.Attribution({
                                className: 'ol-attribution ol-attribution-custom'
                            }),
                            new ol.control.FullScreen({
                                tipLabel: 'Expandir Mapa',
                                className: 'btn-fullscreen',
                                label: '\u2922'
                            })
                        ]);

                        var popup = document.getElementById('popUpInfoDemanda');

                        var overlay = new ol.Overlay({
                            element: popup,
                            autoPan: true,
                            autoPanAnimation: {
                                duration: 250
                            }
                        });

                        var popUpDemandasOF = document.getElementById('popUpDemandasOF');

                        var overlayDemandas = new ol.Overlay({
                            element: popUpDemandasOF
                        });

                        var coordView = [-5330442.433589233, -1780802.4390055723];

                        if (imovel.coordinates) {
                            coordView = imovel.coordinates;
                        }

                        vm.map = new ol.Map({
                            target: element[0].firstChild,
                            controls: controles,
                            crossOrigin: 'anonymous',
                            renderer: 'canvas',
                            overlays: [overlay, overlayDemandas],
                            interactions: ol.interaction.defaults({
                                altShiftDragRotate: false
                            }),
                            view: viewFac.getView2(coordView)
                        });

                        angular.forEach(vm.layers, function(layer) {
                            vm.map.addLayer(layer);
                        });

                        vm.map.getLayerGroup().getLayers().insertAt(vm.map.getLayerGroup().getLayers().getArray().length, imovelLayer);

                        if (vm.visualizar) {
                            vm.map.on('click', function(e) {
                                overlay.setPosition();
                                var features = [];
                                if (vm.map.hasFeatureAtPixel(e.pixel)) {
                                    vm.map.forEachFeatureAtPixel(e.pixel, function(feature) {
                                        features.push(feature);
                                    });
                                    if (features) {
                                        demanda = null;
                                        vm.demanda = null;
                                        for (var i = 0; i < vm.demandas.length; i++) {
                                            if (vm.demandas[i].id === features[0].getId()) {
                                                demanda = vm.demandas[i];
                                                vm.demanda = angular.copy(vm.demandas[i]);
                                                vm.demanda.justificativa = vm.demanda.justificativa.slice(0, 80);
                                                break;
                                            }
                                        }
                                        $timeout(function() {
                                            overlay.setPosition(features[0].getGeometry().getCoordinates());
                                        });
                                    }
                                }
                            });
                        }

                        if (imovel && imovel.coordinates) {

                            geometriaImovelPonto = geoJson.readGeometry(imovel);

                            if (imovel) {
                                feature = new ol.Feature({
                                    geometry: geometriaImovelPonto
                                });
                                feature.setId(featureIds.PONTO_IMOVEL);
                                feature.setStyle(pontoImovel);
                                imovelSource.addFeature(feature);
                            }
                        }

                        if (scope.croqui) {
                            var feature, geometria = geoJson.readGeometry(scope.croqui);
                            feature = new ol.Feature({
                                geometry: geometria
                            });
                            feature.setId(featureIds.SHAPE_FILE);
                            feature.setStyle(benfeitoriaStyle);
                            imovelSource.addFeature(feature);
                            viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());
                        }

                        NotificadorServiceFiscalizacao.subscribe(scope, function(event, escopo, listaCoordenadas) {
                            if (listaCoordenadas && listaCoordenadas.length > 0) {
                                imovelSource.clear();

                                for (var i = 0; i < listaCoordenadas.length; i++) {
                                    var geometria = geoJson.readGeometry(listaCoordenadas[i].coordenadas);
                                    if (geometria) {
                                        var feature = new ol.Feature({
                                            geometry: geometria
                                        });
                                        feature.setId(listaCoordenadas[i].demandaId);
                                        if (listaCoordenadas[i].status === 'EXECUTADA') {
                                            feature.setStyle(styles.estrelaVerde());
                                        } else if (listaCoordenadas[i].status === 'DISPONIVEL') {
                                            if (listaCoordenadas[i].tipoMarcador === 'AMARELO') {
                                                feature.setStyle(styles.pinAmarelo());
                                            }
                                            if (listaCoordenadas[i].tipoMarcador === 'AZUL') {
                                                feature.setStyle(styles.pinAzul());
                                            }
                                            if (listaCoordenadas[i].tipoMarcador === 'VERMELHO') {
                                                feature.setStyle(styles.pinVermelho());
                                            }
                                        } else if (listaCoordenadas[i].status === 'VINCULADA') {
                                            if (listaCoordenadas[i].tipoMarcador === 'AMARELO') {
                                                feature.setStyle(styles.estrelaAmarela());
                                            }
                                            if (listaCoordenadas[i].tipoMarcador === 'AZUL') {
                                                feature.setStyle(styles.estrelaAzul());
                                            }
                                            if (listaCoordenadas[i].tipoMarcador === 'VERMELHO') {
                                                feature.setStyle(styles.estrelaVermelha());
                                            }
                                        } else if (listaCoordenadas[i].status === 'EM_EXECUCAO_POR_AGENTE_EXTERNO') {
                                            feature.setStyle(styles.trianguloCinza());
                                        } else if (listaCoordenadas[i].status === 'EXECUTADA_POR_AGENTE_EXTERNO') {
                                            feature.setStyle(styles.trianguloVerde());
                                        }
                                        imovelSource.addFeature(feature);
                                    }
                                }
                            }

                        }, notifiers.MARCAR_LISTA_COORDENADAS);

                        NotificadorServiceFiscalizacao.subscribe(scope, function(event, escopo, imovel) {
                            imovelSource.clear();
                            var geometria = geoJson.readGeometry(imovel);
                            var feature = new ol.Feature({
                                geometry: geometria
                            });
                            feature.setStyle(pontoImovel);
                            feature.setId(featureIds.PONTO_IMOVEL);
                            setFeature(feature);
                            viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());

                            escopo.latitude = imovel.coordinates[0];
                            escopo.longitude = imovel.coordinates[1];
                        }, notifiers.MARCAR_PONTO_POR_COORDENADAS);

                        NotificadorServiceFiscalizacao.subscribe(scope, function(event, scopo, gemoetriaShapeFile) {

                            var feature, geometria = geoJson.readGeometry(gemoetriaShapeFile);
                            feature = new ol.Feature({
                                geometry: geometria
                            });
                            feature.setId(featureIds.SHAPE_FILE);
                            feature.setStyle(benfeitoriaStyle);
                            imovelSource.addFeature(feature);
                            viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());

                        }, notifiers.MARCAR_PONTOS_SHAPE_FILE);

                        NotificadorServiceFiscalizacao.subscribe(scope, function(event, scopo, gemoetriaShapeFile) {
                            adicionarCentroSegmentos();
                        }, notifiers.LIMPAR_DESENHO);


                        NotificadorServiceFiscalizacao.subscribe(scope, function(event, escopo, imovel) {
                            imovelSource.clear();
                            var feature = new ol.Feature({
                                geometry: null
                            });
                            feature.setStyle(benfeitoriaStyle);
                            feature.setId(featureIds.ACESSAO_BENFEITORIA);

                            imovelSource.addFeature(feature);
                            if (imovelSource.getFeatureById(featureIds.ACESSAO_BENFEITORIA)) {
                                imovelSource.removeFeature(imovelSource.getFeatureById(featureIds.ACESSAO_BENFEITORIA));
                            }

                        }, notifiers.APAGAR_BENFEITORIA_POR_COORDENADAS);

                        NotificadorServiceFiscalizacao.subscribe(scope, function(event, scope, geometriaJson) {
                            var geometria;
                            if (geometriaJson) {
                                geometria = geoJson.readGeometry(geometriaJson);
                                console.log('geometriaJson', geometriaJson);
                                console.log('geometria', geometria);
                                console.log('geometria.getExtent()', geometria.getExtent());
                                viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());
                            }
                        }, notifiers.CENTRALIZAR_POR_MUNICIPIO);


                        NotificadorServiceFiscalizacao.subscribe(scope, function(event, scope, extent) {
                            if (extent) {
                                olMapUtils.zoomByExtent(vm.map, extent);
                            }

                        }, notifiers.CENTRALIZAR);

                    };

                    initHeatMap = function() {
                        var coordView = [-51.4198237555309, -14.2364826698601];

                        NotificadorServiceFiscalizacao.subscribe(scope, function(event, escopo, listaCoordenadas) {
                            if (listaCoordenadas && listaCoordenadas.length > 0) {

                                for (var i = 0, count = 0; i < listaCoordenadas.length; i++) {
                                    if (listaCoordenadas[i].coordenadas.coordinates) {
                                        var feature, geometria = geoJson.readGeometry(listaCoordenadas[i].coordenadas);
                                        feature = new ol.Feature({
                                            geometry: geometria
                                        });

                                        var polyFeatures = imovelLayer.getSource();

                                        polyFeatures.forEachFeature(function(polyFeature) {
                                            if (polyFeature.getGeometry().getType() === 'Polygon') {
                                                var coordsSingle = [];
                                                // this will get you central coordinate of polygon
                                                coordsSingle.push(polyFeature.getGeometry().getInteriorPoint());
                                                var centerCoordinates = angular.copy(coordsSingle[0].B);
                                                var centerGeometria = {};
                                                centerGeometria.coordinates = centerCoordinates;
                                                centerGeometria.type = 'Point';
                                                centerGeometria = geoJson.readGeometry(centerGeometria);

                                                var feature = new ol.Feature({
                                                    geometry: centerGeometria
                                                });

                                                setHeatFeature(feature, count);

                                            }

                                            count++;
                                        });

                                        imovelSource.addFeature(feature);
                                    } else {
                                        var geometria = geoJson.readGeometry(listaCoordenadas[i].coordenadas);

                                        if (geometria) {
                                            var feature = new ol.Feature({
                                                geometry: geometria
                                            });
                                            feature.setId(listaCoordenadas[i].demandaId);

                                            imovelSource.addFeature(feature);
                                        }
                                    }
                                }
                            }

                        }, notifiers.MARCAR_LISTA_COORDENADAS);

                        var heatMap = new ol.layer.Heatmap({
                            source: imovelSource
                        });

                        var osmLayer = new ol.layer.Tile({
                            source: new ol.source.Stamen({
                                layer: 'toner-lite'
                            })
                        });

                        vm.map = new ol.Map({
                            target: element[0].firstChild,
                            layers: [osmLayer, heatMap],
                            renderer: 'canvas',
                            interactions: ol.interaction.defaults({
                                altShiftDragRotate: false
                            }),
                            view: viewFac.getView3(coordView)
                        });
                    };

                    initColorMap = function() {
                        benfeitoriaStyle = styles.terrenoVerde();

                        var azul = '#244E76';
                        var laranja = '#EC7D3A';
                        var verde = '#07AF58';
                        var vermelho = '#BE0000';

                        for (var i = 0; i < scope.estados.length; i++) {
                            if (scope.estados[i].porcentagem < 40) {
                                scope.estados[i].cor = vermelho;
                            } else if (scope.estados[i].porcentagem < 60) {
                                scope.estados[i].cor = laranja;
                            } else if (scope.estados[i].porcentagem < 80) {
                                scope.estados[i].cor = verde;
                            } else {
                                scope.estados[i].cor = azul;
                            }
                        }

                        var controles = ol.control.defaults().extend([
                            new ol.control.ScaleLine(),
                            new ol.control.Attribution({
                                className: 'ol-attribution ol-attribution-custom'
                            }),
                            new ol.control.FullScreen({
                                tipLabel: 'Expandir Mapa',
                                className: 'btn-fullscreen',
                                label: '\u2922'
                            })
                        ]);

                        var coordView = [-5330442.433589233, -1780802.4390055723];

                        vm.map = new ol.Map({
                            target: element[0].firstChild,
                            controls: controles,
                            renderer: 'canvas',
                            interactions: ol.interaction.defaults({
                                altShiftDragRotate: false
                            }),
                            view: viewFac.getView3(coordView)
                        });

                        angular.forEach(vm.layers, function(layer) {
                            vm.map.addLayer(layer);
                        });

                        vm.map.getLayerGroup().getLayers().insertAt(vm.map.getLayerGroup().getLayers().getArray().length, imovelLayer)
                    };

                    setFeature = function(feature) {
                        feature.setId(featureIds.PONTO_IMOVEL);
                        feature.setStyle(pontoImovel);
                        imovelSource.addFeature(feature);
                    };

                    setHeatFeature = function(feature, count) {
                        feature.setId(featureIds.PONTO_IMOVEL + '.' + count);
                        feature.setStyle(pontoImovel);
                        imovelSource.addFeature(feature);
                    };

                    removerCustomInteractions = function() {
                        vm.map.removeInteraction(drawPoint);
                        vm.map.removeInteraction(drawPolygon);
                        vm.map.removeInteraction(drawPolygonBenfeitoria);
                        vm.map.removeInteraction(drawPolygonUnidadeAutonoma);
                        vm.map.removeInteraction(erase);
                    };

                    setDrawPoint = function() {
                        drawPoint = new ol.interaction.Draw({
                            source: imovelSource,
                            type: 'Point',
                            style: pontoImovel
                        });
                    };

                    vm.desbloquearMapa = function() {
                        vm.bloquearMapa = false;
                        vm.marcarPonto();
                    };

                    vm.changeJustificativaOnMouseOver = function() {
                        vm.demanda.justificativa = demanda.justificativa.slice(0, 400);
                    };

                    vm.changeJustificativaOnMouseLeave = function() {
                        vm.demanda.justificativa = demanda.justificativa.slice(0, 80);
                    };

                    vm.marcarPonto = function() {
                        if (viewFac.getView().getZoom() >= 13) {

                            removerCustomInteractions();

                            setDrawPoint();

                            vm.map.addInteraction(drawPoint);

                            drawPoint.on('drawend', function(e) {
                                /** PEGA A ELEMENTO DO PONTO MARCADO NO MAPA*/
                                var feature = e.feature;
                                feature.setId(featureIds.PONTO_IMOVEL);
                                /** LIMPA O PONTO ANTERIOR*/
                                if (imovelSource.getFeatureById(featureIds.PONTO_IMOVEL)) {
                                    imovelSource.removeFeature(imovelSource.getFeatureById(featureIds.PONTO_IMOVEL));
                                }
                                /** ADICIONA O ESTILO AO PONTO SELECIONADO*/
                                feature.setStyle(pontoImovel);
                                imovel.coordinates = geraCoordenadas(feature);
                                imovel.type = 'Point';
                                vm.map.removeInteraction(drawPoint);
                                vm.validar({ imovel: imovel });
                                if (vm.exportarImagem) {
                                    scope.centralizarMapa(imovel, function() {
                                        vm.exportarImg();
                                    });

                                }
                            });
                        } else {
                            ComumFactory.mostrarMensagemError('Para incluir um ponto é necessário a aproximação igual ou inferior a 2000 metros');
                        }
                    };

                    function geraCoordenadas(e) {
                        var coords = e.getGeometry().getCoordinates();
                        $timeout(function() {
                            vm.latitude = (coords[0]);
                            vm.longitude = (coords[1]);
                            NotificadorServiceFiscalizacao.notify(vm, coords, notifiers.ATUALIZAR_COORDENADAS_DO_PONTO);
                        }, 100);
                        return coords;
                    }

                    vm.desenharBenfeitoria = function() {
                        if (viewFac.getView().getZoom() >= 13) {

                            setDrawPolygonBenfeitoria();
                            vm.map.addInteraction(drawPolygonBenfeitoria);

                            drawPolygonBenfeitoria.on('drawend', function(e) {
                                var feature = e.feature;
                                feature.setId(featureIds.ACESSAO_BENFEITORIA);
                                feature.setStyle(benfeitoriaStyle);

                                imovelSource.addFeature(feature);
                                if (imovelSource.getFeatureById(featureIds.ACESSAO_BENFEITORIA)) {
                                    imovelSource.removeFeature(imovelSource.getFeatureById(featureIds.ACESSAO_BENFEITORIA));
                                }
                                vm.map.removeInteraction(drawPolygonBenfeitoria);
                                scope.croqui = {
                                    type: 'Polygon',
                                    coordinates: e.feature.getGeometry().getCoordinates()
                                };

                                if (vm.validarPoligono) {
                                    var imovel = {};
                                    imovel.coordinates = scope.croqui.coordinates[0][0];
                                    imovel.type = 'Point';

                                    vm.validarPoligono({ imovel: imovel });
                                }

                                scope.centralizarMapa(scope.croqui, function() {
                                    vm.exportarImg();
                                });

                            });
                        }
                    };

                    vm.limpar = function() {

                        vm.latitude = '';
                        vm.longitude = '';
                        if (imovelSource.getFeatureById(featureIds.ACESSAO_BENFEITORIA)) {
                            imovelSource.removeFeature(imovelSource.getFeatureById(featureIds.ACESSAO_BENFEITORIA));
                        }
                        if (imovelSource.getFeatureById(featureIds.PONTO_IMOVEL)) {
                            imovelSource.removeFeature(imovelSource.getFeatureById(featureIds.PONTO_IMOVEL));
                        }
                        return false;
                    };

                    vm.geraKml = function() {
                        var format = new ol.format.KML();
                        var kml = format.writeFeatures([new ol.Feature(new ol.geom.Point([vm.latitude, vm.longitude]))], { featureProjection: vm.map.getView().getProjection() });
                        vm.exportarMapa({ kml: kml });
                    };

                    vm.alterarBingVisible = function() {
                        angular.forEach(vm.map.getLayers(), function(value) {
                            if (value.get('name') === 'bingMapa') {
                                value.setVisible(!value.getVisible());
                                NotificadorServiceFiscalizacao.notify(scope, value.getVisible(), notifiers.ALTERAR_VISIBILIDADE_BING);
                            }
                        });

                    };

                    setDrawPolygonBenfeitoria = function() {
                        drawPolygonBenfeitoria = new ol.interaction.Draw({
                            source: imovelSource,
                            type: 'Polygon'
                        });
                    };

                    vm.alterarLayerVisible = function(name) {
                        angular.forEach(vm.map.getLayers(), function(value) {
                            if (value.get('name') === name) {
                                value.setVisible(!value.getVisible());
                            }
                        });
                    };

                    vm.visualizarDemanda = function() {
                        var url = $state.href('fiscalizacao.demanda.consultar', { id: demanda.id });
                        demanda = null;
                        window.open(url, '_blank');
                    };

                    vm.adicionarDemanda = function() {
                        $timeout(function() {
                            NotificadorServiceFiscalizacao.notify(vm, demanda, notifiers.ADICIONAR_DEMANDAS);
                        });
                    };
                    vm.exportarImg = function() {
                        vm.map.on('postcompose', function(event) {
                            var canvas = event.context.canvas;
                            vm.imgMapa = canvas.toDataURL('image/png', 1.0);
                        });
                    };

                    vm.centralizarMapa = function(geometria, callback) {
                        var geometria = geoJson.readGeometry(geometria);
                        viewFac.centralizarMapa(geometria.getExtent(), vm.map.getSize());
                        if (callback) {
                            callback();
                        }
                    };

                    function adicionarCentroSegmentos() {
                        var featureShapeFile = imovelSource.getFeatureById(featureIds.SHAPE_FILE),
                            polygon,
                            line,
                            ponto,
                            centroFeature,
                            i;

                        imovelSource.removeFeature(imovelSource.getFeatureById(featureIds.SHAPE_FILE));

                        if (featureShapeFile) {
                            polygon = featureShapeFile.getGeometry();
                        }

                        if (polygon) {
                            for (i = 1; i < polygon.getCoordinates()[0].length; i += 1) {
                                line = new ol.geom.LineString([polygon.getCoordinates()[0][i - 1], polygon.getCoordinates()[0][i]]);
                                ponto = new ol.geom.Point(ol.extent.getCenter(line.getExtent()));

                                centroFeature = new ol.Feature({
                                    geometry: ponto,
                                    name: featureIds.SHAPE_FILE
                                });
                                centroFeature.setStyle(benfeitoriaStyle);
                                imovelSource.addFeature(centroFeature);
                            }
                        }

                    }

                    vm.$on('enquadrarUf', function(event, geomWKT) {
                        enquadrarUf(geomWKT);
                    });

                    function enquadrarUf(geomWKT) {
                        if (geomWKT) {
                            vm.map.getView().setCenter(ol.proj.transform(getCenterOfExtent(geomWKT.getExtent()), 'EPSG:4326', 'EPSG:3857'));
                            vm.map.getView().setZoom(5);
                        }
                    }

                    function getCenterOfExtent(Extent) {
                        var X = Extent[0] + (Extent[2] - Extent[0]) / 2;
                        var Y = Extent[1] + (Extent[3] - Extent[1]) / 2;
                        return [X, Y];
                    }

                    if (vm.tipo && vm.tipo === 'calor') {
                        initHeatMap();
                    } else
                    if (vm.tipo && vm.tipo === 'colorido') {
                        initColorMap();
                    } else {
                        init();
                    }

                });
            }
        };
    }]);
}());

(function () {
    'use strict';
    angular.module('su-fiscalizacao').factory('RelatorioService', ["$http", "Upload", "$window", function ($http, Upload, $window) {

        var baseUrl = '/fiscalizacao/api/relatorio-fiscalizacao';
        var baseUrlDocumento = '/fiscalizacao/api/documentos';
        var uploadUrl = '/fiscalizacao/api/arquivo';

        var concluir = function (registro) {
            return $http.post(baseUrl + '/concluir', registro);
        };

        var gerar = function (registro) {
            return $http.post(baseUrl + '/gerar', registro);
        };

        var salvar = function (registro) {
            return $http.post(baseUrl + '/salvar', registro);
        };

        var autuadoProcessoSei = function (autuado) {
            return $http.post(baseUrl + '/autuado/gerarProcessoSei', autuado);
        };

        var getOrdemFiscalizacao = function () {
            return $http.get(baseUrl + '/ordemFiscalizacoes');
        };

        var getOrdensFiscalizacaoPorJurisdicao = function() {
            return $http.get(baseUrl + '/ordemFiscalizacoesPorJurisdicao');
        }

        var getDocumentos = function (registro) {
            return $http.get(baseUrl + '/documentos', registro);
        };

        var getMotivacoes = function () {
            return $http.get(baseUrl + "/motivacoes");
        };

        var findByFilter = function (registro) {
            return $http.post(baseUrl + "/filtro?page=" + registro.page + "&size=" + registro.size + "&sort=" + registro.order + "," + registro.orderBy, registro);
        };

        var getAssuntos = function () {
            return $http.get(baseUrl + "/assuntos");
        };

        var getTiposImovel = function () {
            return $http.get(baseUrl + "/tiposImovel");
        };

        var getZoneamentos = function () {
            return $http.get(baseUrl + "/zoneamentos");
        };

        var getRelatorioOrdemFiscalizacao = function (ordemFiscalizacaoId) {
            return $http.get(baseUrl + '/relatorios/' + ordemFiscalizacaoId);
        };

        var getRelatorioOrdemFiscalizacaoEmJurisdicao = function (ordemFiscalizacaoId) {
            return $http.get(baseUrl + '/relatorios-em-jurisdicao/' + ordemFiscalizacaoId);
        };

        var getStatus = function () {
            return $http.get(baseUrl + '/status');
        };

        var getTipoLogradouro = function () {
            return $http.get(baseUrl + "/tipoLogradouro");
        };
        var deleteRelatorio = function (registro) {
            return $http.post(baseUrl + "/delete", registro);
        };
        var findBy = function (registroId) {
            return $http.get(baseUrl + "/" + registroId);
        };
        var getDescricaoBySearch = function (registro) {
            return $http.post(baseUrl + "/search", registro);
        };

        var getDemandasByProtocolo = function (protocolo) {
            return $http.post(baseUrl + '/demandas-by-protocolo/', protocolo);
        };

        var getTiposDocumento = function () {
            return $http.get(baseUrlDocumento + '/tipos-documento');
        };

        var getDocumentoStatus = function () {
            return $http.get(baseUrlDocumento + '/documento-status');
        };

        var getTiposNotificacao = function () {
            return $http.get(baseUrlDocumento + '/tipos-notificacao');
        };

        var documentosIdentificacao = function () {
            return $http.get(baseUrlDocumento + '/documentos-identificacao');
        };

        var getUsuario = function () {
            return $http.get(baseUrl + "/usuario");
        };

        var validar = function (id) {
            return $http.post(baseUrl + "/validar", id);
        };

        var upload = function($file){
            return Upload.upload({
                url: uploadUrl + '/upload',
                data: {
                    file: $file
                }
            });
        };

        var uploadShapeFile = function($file){
            return Upload.upload({
                url: uploadUrl + '/upload-shape-file',
                data: {
                    file: $file
                }
            });
        };

        var getShapeFile = function (id) {
            return $http.get(baseUrl + "/arquivo/" + id);
        };

        var getValorMulta = function () {
            return $http.get(baseUrlDocumento + "/valor-multa");
        };

        var editarDocumento = function (documento) {
            return $http.post(baseUrlDocumento + '/edit-documento', documento);
        };

        var excluirDocumento = function (documento) {
            return $http.post(baseUrlDocumento + '/delete', documento);
        };

        var autuarDocumento = function (relatorioId, documentoId) {
            return $http.get(baseUrlDocumento + '/autuar/' + relatorioId + '/' + documentoId);
        };

        var exportarCsv = function (filtro) {
            var request = angular.copy(filtro);
            return $http.post(baseUrl + '/exportarCsv', request);
        };

        return {
            concluir: concluir,
            gerar: gerar,
            getOrdemFiscalizacao: getOrdemFiscalizacao,
            getOrdensFiscalizacaoPorJurisdicao: getOrdensFiscalizacaoPorJurisdicao,
            getDocumentos: getDocumentos,
            getRelatorioOrdemFiscalizacao: getRelatorioOrdemFiscalizacao,
            getRelatorioOrdemFiscalizacaoEmJurisdicao: getRelatorioOrdemFiscalizacaoEmJurisdicao,
            getStatus: getStatus,
            getMotivacoes: getMotivacoes,
            getAssuntos: getAssuntos,
            getTiposImovel: getTiposImovel,
            getTiposDocumento: getTiposDocumento,
            getDocumentoStatus: getDocumentoStatus,
            getTiposNotificacao: getTiposNotificacao,
            getZoneamentos: getZoneamentos,
            getTipoLogradouro: getTipoLogradouro,
            salvar: salvar,
            getUsuario: getUsuario,
            documentosIdentificacao: documentosIdentificacao,
            findByFilter: findByFilter,
            deleteRelatorio: deleteRelatorio,
            findBy: findBy,
            getDescricaoBySearch: getDescricaoBySearch,
            getDemandasByProtocolo: getDemandasByProtocolo,
            upload: upload,
            uploadShapeFile: uploadShapeFile,
            getShapeFile: getShapeFile,
            validar:validar,
            getValorMulta:getValorMulta,
            editarDocumento: editarDocumento,
            excluirDocumento: excluirDocumento,
            autuarDocumento: autuarDocumento,
            autuadoProcessoSei: autuadoProcessoSei,
            exportarCsv: exportarCsv
        }
    }]);
})();

(function () {
        'use strict';
        angular.module('su-fiscalizacao').factory('OrdemFiscalizacaoService', ["$http", function ($http) {

            var baseUrl = '/fiscalizacao/api/ordem-fiscalizacao';
            var baseUrlAgenteExterno = '/fiscalizacao/api/fiscalizacao-agente-externo';
            var integradorUrl = '/fiscalizacao/api/integracoes';

            var create = function (registro) {
                return $http.post(baseUrl, registro);
            };

            var update = function (registro) {
                return $http.put(baseUrl, registro);
            };

            var validar = function (registro) {
                return $http.post(baseUrl + '/validar', registro);
            };

            var revalidar = function (registro) {
                return $http.post(baseUrl + '/revalidar', registro);
            };

            var rejeitar = function (registro) {
                return $http.post(baseUrl + '/rejeitar', registro);
            };

            var findByFilter = function (registro) {
                return $http.post(baseUrl + "/filtro?page=" + registro.page + "&size=" + registro.size + "&sort=" + registro.order + "," + registro.orderBy, registro);
            };

            var findByFilterAgenteExterno = function (registro) {
                return $http.post(baseUrlAgenteExterno + "/filtro?page=" + registro.page + "&size=" + registro.size + "&sort=" + registro.order + "," + registro.orderBy, registro);
            };

            var findBy = function (registroId) {
                return $http.get(baseUrl + "/" + registroId);
            };

            var deleteOrdemFiscalizacao = function (registro) {
                return $http.post(baseUrl + "/delete", registro);
            };

        
            var buscarDemandas = function (filtros,registro) {
                return $http.post(baseUrl + "/demanda?page=" + filtros.page + "&size=" + filtros.size + "&sort="+ filtros.order+ "," + filtros.orderBy, registro);
            };

            var buscarAllDemandasByUF = function (filtros) {
                return $http.post(baseUrl + "/demanda/all?page=" + filtros.page + "&size=" + filtros.size + "&sort="+ filtros.order+ "," + filtros.orderBy);
            };

            var buscarFiscais = function () {
                return $http.get(integradorUrl + "/acessos/fiscais");
            };

            var buscarFiscal = function (id) {
                return $http.get(integradorUrl + "/siape/fiscal/"+id);
            };

            var getHistorico = function (demandaId) {
                return $http.get(baseUrl + "/historico/" + demandaId);
            };

            var rascunho = function (registro) {
                return $http.post(baseUrl + "/rascunho",registro);
            };

            var getUsuario = function () {
                return $http.get(baseUrl + "/usuario");
            };

            var getStatus = function () {
                return $http.get(baseUrl + "/status");
            };

            var getStatusAgenteExterno = function () {
                return $http.get(baseUrlAgenteExterno + "/status");
            };

            var getDemanda = function(demandaId){
                return $http.get(baseUrl + "/demanda/" + demandaId);
            };

            var cancelarFiscalizacaoAgenteExterno = function (registro) {
                return $http.post(baseUrlAgenteExterno + "/cancelar", registro);
            };

            return {
                create: create,
                update: update,
                validar: validar,
                rejeitar: rejeitar,
                revalidar: revalidar,
                findByFilter: findByFilter,
                findBy: findBy,
                deleteOrdemFiscalizacao: deleteOrdemFiscalizacao,
                buscarDemandas: buscarDemandas,
                buscarAllDemandasByUF: buscarAllDemandasByUF,
                buscarFiscais: buscarFiscais,
                getHistorico: getHistorico,
                rascunho:rascunho,
                getUsuario : getUsuario,
                getStatus: getStatus,
                getStatusAgenteExterno: getStatusAgenteExterno,
                buscarFiscal:buscarFiscal,
                getDemanda: getDemanda,
                findByFilterAgenteExterno: findByFilterAgenteExterno,
                cancelarFiscalizacaoAgenteExterno: cancelarFiscalizacaoAgenteExterno
            }
        }])
    }
)();

'use strict';
angular.module('su-fiscalizacao').controller("GlobalController", [controller]);

function controller(){

}

(function () {
    'use strict';
    angular.module('su-fiscalizacao')
        .factory('DemandaService', ["$http", function ($http) {
            var baseUrl = '/fiscalizacao/api/demandas';
            var baseUrlMapa = '/fiscalizacao/api/mapa-demanda';

            var create = function (registro) {
                return $http.post(baseUrl, registro);
            };

            var update = function (registro) {
                return $http.put(baseUrl, registro);
            };

            var findByFilter = function (registro) {
                return $http.post(baseUrl + '/filtro?page=' + registro.page + '&size=' + registro.size + '&sort=' + registro.order + ',' + registro.orderBy, registro);
            };

            var findBy = function (registroId) {
                return $http.get(baseUrl + '/' + registroId);
            };

            var getOrdemFiscalizacaoByOfId = function (ordemFiscalizacaoId) {
                return $http.get(baseUrl + '/ordem-fiscalizacao/' + ordemFiscalizacaoId);
            };

            var excluirDemanda = function (registro) {
                return $http.post(baseUrl + '/delete', registro);
            };

            var getMotivacoes = function () {
                return $http.get(baseUrl + '/motivacoes');
            };

            var getAssuntos = function () {
                return $http.get(baseUrl + '/assuntos');
            };

            var getTiposImovel = function () {
                return $http.get(baseUrl + '/tiposImovel');
            };

            var getZoneamentos = function () {
                return $http.get(baseUrl + '/zoneamentos');
            };

            var getHistorico = function (demandaId) {
                return $http.get(baseUrl + '/historico/' + demandaId);
            };

            var getEdesc = function () {
                return $http.get(baseUrl + '/edesc');
            };

            var getStatus = function () {
                return $http.get(baseUrl + '/status');
            };

            var getUsuario = function () {
                return $http.get(baseUrl + '/usuario');
            };

            var getTipoLogradouro = function () {
                return $http.get(baseUrl + '/tipoLogradouro');
            };

            var suspender = function (id) {
                return $http.get(baseUrl + '/suspender/' + id);
            };

            var restaurar = function (id) {
                return $http.get(baseUrl + '/restaurar/' + id);
            };

            var getOrdemFiscalizacaoByDemandaId = function (demandaId) {
                return $http.get(baseUrl + '/ordemfiscalizacao/' + demandaId);
            };

            var getRelatorioOrdemFiscalizacaoByDemandaId = function (demandaId) {
                return $http.get(baseUrl + '/relatoriofiscalizacao/' + demandaId);
            };

            var buscarAllDemandasByUF = function (filtros) {
                return $http.post(baseUrlMapa + '/all?page=' + filtros.page + '&size=' + filtros.size + '&sort=' + filtros.order + ',' + filtros.orderBy);
            };

            var buscar = function (filtro) {
                return $http.post(baseUrlMapa + '/buscar', filtro);
            };

            return {
                update: update,
                create: create,
                findByFilter: findByFilter,
                findBy: findBy,
                getOrdemFiscalizacaoByOfId: getOrdemFiscalizacaoByOfId,
                excluirDemanda: excluirDemanda,
                getMotivacoes: getMotivacoes,
                getAssuntos: getAssuntos,
                getTiposImovel: getTiposImovel,
                getZoneamentos: getZoneamentos,
                getHistorico: getHistorico,
                getEdesc: getEdesc,
                getStatus: getStatus,
                getUsuario: getUsuario,
                getTipoLogradouro: getTipoLogradouro,
                suspender: suspender,
                restaurar: restaurar,
                buscarAllDemandasByUF: buscarAllDemandasByUF,
                getOrdemFiscalizacaoByDemandaId: getOrdemFiscalizacaoByDemandaId,
                getRelatorioOrdemFiscalizacaoByDemandaId: getRelatorioOrdemFiscalizacaoByDemandaId,
                buscar: buscar
            };
        }]);
})();

(function () {
  'use strict';
  var moduleName = "ngMaterialDatePicker";

  var VIEW_STATES = {
    DATE: 0,
    HOUR: 1,
    MINUTE: 2
  };

  var css = function (el, name) {
    el = angular.element(el);
    return ('getComputedStyle' in window) ? window.getComputedStyle(el[0])[name] : el.css(name);
  };

  var template = '<md-dialog class="dtp" layout="column" style="width: 300px;">'
    + '    <md-dialog-content class="dtp-content">'
    + '        <div class="dtp-date-view">'
    + '            <header class="dtp-header">'
    + '                <div class="dtp-actual-day" ng-show="picker.dateMode">{{picker.currentDate.format("dddd")}}</div>'
    + '                <div class="dtp-actual-day" ng-show="picker.timeMode">{{picker.params.shortTime ? picker.currentDate.format("A") : " "}}</div>'
    + '                <div class="dtp-close text-right">'
    + '                    <a href="#" mdc-dtp-noclick ng-click="picker.hide()">&times;</a>'
    + '                </div>'
    + '            </header>'
    + '            <div class="dtp-date" ng-show="picker.params.date">'
    + '                <div layout="column">'
    + '                    <div class="dtp-actual-month">{{picker.currentDate.format("MMM") | uppercase}}</div>'
    + '                </div>'
    + '                <div class="dtp-actual-num">{{picker.currentDate.format("DD")}}</div>'
    + '                <div layout="row">'
    + ' <div ng-click="picker.incrementYear(-1)" class="dtp-year-btn dtp-year-btn-prev" flex="30"><span ng-if="picker.isPreviousYearVisible()" >&#x25B2;</span></div>'
    + '                    <div class="dtp-actual-year" flex>{{picker.currentDate.format("YYYY")}}</div>'
    + ' <div ng-click="picker.incrementYear(1)" class="dtp-year-btn dtp-year-btn-next" flex="30"><span ng-if="picker.isNextYearVisible()" >&#x25BC;</span></div>'
    + '                </div>'
    + '            </div>'//start time
    + '            <div class="dtp-time" ng-show="picker.params.time && !picker.params.date">'
    + '                <div class="dtp-actual-maxtime">{{picker.currentNearest5Minute().format(picker.params.shortTime ? "hh:mm" : "HH:mm")}}</div>'
    + '            </div>'
    + '            <div class="dtp-picker">'
    + '                <mdc-datetime-picker-calendar date="picker.currentDate" picker="picker" class="dtp-picker-calendar" ng-show="picker.currentView === picker.VIEWS.DATE"></mdc-datetime-picker-calendar>'
    + '                <div class="dtp-picker-datetime" ng-cloak ng-if="picker.currentView !== picker.VIEWS.DATE">'
    + '                    <div class="dtp-actual-meridien">'
    + '                        <div class="left p20">'
    + '                            <a href="#" mdc-dtp-noclick class="dtp-meridien-am" ng-class="{selected: picker.meridien == \'AM\'}" ng-click="picker.selectAM()">{{picker.params.amText}}</a>'
    + '                        </div>'
    + '                        <div ng-show="!picker.timeMode" class="dtp-actual-time p60">{{picker.currentNearest5Minute().format(picker.params.shortTime ? "hh:mm" : "HH:mm")}}</div>'
    + '                        <div class="right p20">'
    + '                            <a href="#" mdc-dtp-noclick class="dtp-meridien-pm" ng-class="{selected: picker.meridien == \'PM\'}" ng-click="picker.selectPM()">{{picker.params.pmText}}</a>'
    + '                        </div>'
    + '                        <div class="clearfix"></div>'
    + '                    </div>'
    + '                    <mdc-datetime-picker-clock mode="hours" ng-if="picker.currentView === picker.VIEWS.HOUR"></mdc-datetime-picker-clock>'
    + '                    <mdc-datetime-picker-clock mode="minutes" ng-if="picker.currentView === picker.VIEWS.MINUTE"></mdc-datetime-picker-clock>'
    + '                </div>'
    + '            </div>'
    + '        </div>'
    + '    </md-dialog-content>'
    + '    <md-dialog-actions class="dtp-buttons">'
    + '            <md-button class="dtp-btn-ok md-button" ng-click="picker.today()"> {{picker.params.todayText}}</md-button>'
    + '            <md-button class="dtp-btn-cancel md-button" ng-click="picker.cancel()"> {{picker.params.cancelText}}</md-button>'
    + '            <md-button class="dtp-btn-ok md-button" ng-click="picker.ok()"> {{picker.params.okText}}</md-button>'
    + '      </md-dialog-actions>'
    + '</md-dialog>';


  angular.module(moduleName, ['ngMaterial'])
    .provider('mdcDatetimePickerDefaultLocale', function () {
      this.locale = window.navigator.userLanguage || window.navigator.language || 'pt';

      this.$get = function () {
        return this;
      };

      this.setDefaultLocale = function (localeString) {
        this.locale = localeString;
      };
    })
    .factory('mdcDefaultParams', ['mdcDatetimePickerDefaultLocale', function (mdcDatetimePickerDefaultLocale) {
      var default_params = {
        date: true,
        time: true,
        format: 'DD-MM-YYYY',
        minDate: null,
        maxDate: null,
        currentDate: null,
        lang: mdcDatetimePickerDefaultLocale.locale,
        weekStart: 0,
        shortTime: false,
        cancelText: 'Cancelar',
        okText: 'OK',
        amText: 'AM',
        pmText: 'PM',
        todayText: 'Hoje',
        disableDates: []
      };
      return default_params;
    }])
    .directive('mdcDatetimePicker', ['$mdDialog', '$timeout',
      function ($mdDialog, $timeout) {

        return {
          restrict: 'A',
          require: 'ngModel',
          scope: {
            currentDate: '=ngModel',
            ngChange: '&',
            time: '=',
            date: '=',
            minDate: '=',
            maxDate: '=',
            disableDates: '=',
            shortTime: '=',
            weekStart: '=',
            format: '@',
            cancelText: '@',
            okText: '@',
            lang: '@',
            amText: '@',
            pmText: '@',
            showTodaysDate: '@',
            todayText: '@'
          },
          link: function (scope, element, attrs, ngModel) {
            var isOn = false;
            if (!scope.format) {
              if (scope.date && scope.time) {
                scope.format = 'DD/MM/YYYY HH:mm';
              } else if (scope.date) {
                scope.format = 'DD/MM/YYYY';
              } else {
                scope.format = 'HH:mm';
              }
            }

            var dateOfTheDay = null;
            if (scope.showTodaysDate !== undefined && scope.showTodaysDate !== "false") {
              dateOfTheDay = moment();
            }

            if (angular.isString(scope.currentDate) && scope.currentDate !== '') {
              scope.currentDate = moment(scope.currentDate, scope.format);
            }

            if (ngModel) {
              ngModel.$formatters.push(function (value) {
                if (typeof value === 'undefined') {
                  return;
                }
                var m = moment(value);
                return m.isValid() ? m.format(scope.format) : '';
              });
            }

            element.attr('readonly', '');
            //@TODO custom event to trigger input
            element.on('focus', function (e) {
              e.preventDefault();
              element.blur();
              if (isOn) {
                return;
              }
              isOn = true;
              var options = {};
              for (var i in attrs) {
                if (scope.hasOwnProperty(i) && !angular.isUndefined(scope[i])) {
                  options[i] = scope[i];
                }
              }
              options.currentDate = scope.currentDate;
              options.showTodaysDate = dateOfTheDay;

              var locals = {options: options};
              $mdDialog.show({
                template: template,
                controller: PluginController,
                controllerAs: 'picker',
                locals: locals,
                openFrom: element,
                parent: angular.element(document.body),
                bindToController: true,
                disableParentScroll: false,
                hasBackDrop: false,
                skipHide: true
              })
                .then(function (v) {
                  scope.currentDate = v ? v._d : v;
                  isOn = false;

                  if (!moment(scope.currentDate).isSame(options.currentDate)) {
                    $timeout(scope.ngChange, 0);
                  }

                  element.blur()

                }, function () {
                  isOn = false;
                  element.blur()
                })
              ;
            });
          }
        };
      }])
    /** Returns a service that opens a dialog when the attribute shown is called
     The dialog serves to select a date/time/etc. depending on the options given to the function show

     @param options extends mdcDefaultParams
     {
       date: {boolean} =true,
         time: {boolean] =true,
         format: {string} ='YYYY-MM-DD',
         minDate: {strign} =null,
         maxDate: {string} =null,
         currentDate: {string} =null,
         lang: {string} =mdcDatetimePickerDefaultLocale,
         weekStart: {int} =0,
         shortTime: {boolean} =false,
         cancelText: {string} ='Cancel',
         todayText: {string} ='Today',
         okText: {string} ='OK',
         amText: {string} ='AM',
         pmText: {string} ='PM'
     }
   @return promise
    */
    .factory('mdcDateTimeDialog', ["$mdDialog", "$q", "mdcDefaultParams", function ($mdDialog, $q, mdcDefaultParams) {
      var accepted_options = Object.keys(mdcDefaultParams);
      var service = {
        show: function (options) {
          var deferred = $q.defer();
          var params = angular.copy(mdcDefaultParams);
          for (var i in options) {
            if (accepted_options.indexOf[i] != -1 && options.hasOwnProperty(i)) {
              params = options[i];
            }
          }
          var locals = {options: options};
          $mdDialog.show({
            template: template,
            controller: PluginController,
            controllerAs: 'picker',
            locals: locals,
            parent: angular.element(document.body),
            bindToController: true,
            clickOutsideToClose: true,
            disableParentScroll: false,
            skipHide: true
          })
            .then(function (v) {
              var currentDate = v ? v._d : v;
              deferred.resolve(v ? v._d : v);
            }, function () {
              deferred.reject();
            });
          return deferred.promise;
        }
      };

      return service;
    }])
  ;

  var PluginController = function ($scope, $mdDialog, mdcDefaultParams) {
    this.currentView = VIEW_STATES.DATE;
    this._dialog = $mdDialog;

    this._attachedEvents = [];
    this.VIEWS = VIEW_STATES;
    this.params = mdcDefaultParams;
    this.meridien = 'AM';
    this.params = angular.extend(this.params, this.options);

    this.init();
  };

  PluginController.$inject = ['$scope', '$mdDialog', 'mdcDefaultParams'];
  PluginController.prototype = {
    init: function () {
      this.timeMode = this.params.time && !this.params.date;
      this.dateMode = this.params.date;
      this.initDates();
      this.start();
    },
    currentNearest5Minute: function () {
      var date = this.currentDate || moment();
      var minutes = (5 * Math.round(date.minute() / 5));
      if (minutes >= 60) {
        minutes = 55; //always push down
      }
      return moment(date).minutes(minutes);
    },
    initDates: function () {
      var that = this;
      var _dateParam = function (input, fallback) {
        var ret = null;
        if (angular.isDefined(input) && input !== null && input !== '') {
          if (angular.isString(input)) {
            if (typeof(that.params.format) !== 'undefined' && that.params.format !== null) {
              ret = moment(input, that.params.format).locale(that.params.lang);
            }
            else {
              ret = moment(input).locale(that.params.lang);
            }
          } else if (typeof input === 'number') {
            ret = moment(input).locale(that.params.lang);
          } else {
            if (angular.isDate(input)) {
              var x = input.getTime();
              ret = moment(x, "x").locale(that.params.lang);
            } else if (input._isAMomentObject) {
              ret = input;
            }
          }
        }
        else {
          ret = fallback;
        }
        return ret;
      };

      this.currentDate = _dateParam(this.params.currentDate, moment());
      this.minDate = _dateParam(this.params.minDate);
      this.maxDate = _dateParam(this.params.maxDate);
      this.disableDates = this.params.disableDates.map(function (x) {
        return moment(x).format('MMMM Do YYYY')
      });
      this.selectDate(this.currentDate);
    },
    initDate: function () {
      this.currentView = VIEW_STATES.DATE;
    },
    initHours: function () {
      this.currentView = VIEW_STATES.HOUR;
    },
    initMinutes: function () {
      this.currentView = VIEW_STATES.MINUTE;
    },
    isAfterMinDate: function (date, checkHour, checkMinute) {
      var _return = true;

      if (typeof(this.minDate) !== 'undefined' && this.minDate !== null) {
        var _minDate = moment(this.minDate);
        var _date = moment(date);

        if (!checkHour && !checkMinute) {
          _minDate.hour(0);
          _minDate.minute(0);

          _date.hour(0);
          _date.minute(0);
        }

        _minDate.second(0);
        _date.second(0);
        _minDate.millisecond(0);
        _date.millisecond(0);

        if (!checkMinute) {
          _date.minute(0);
          _minDate.minute(0);

          _return = (parseInt(_date.format("X")) >= parseInt(_minDate.format("X")));
        }
        else {
          _return = (parseInt(_date.format("X")) >= parseInt(_minDate.format("X")));
        }
      }

      return _return;
    },
    isBeforeMaxDate: function (date, checkTime, checkMinute) {
      var _return = true;

      if (typeof(this.maxDate) !== 'undefined' && this.maxDate !== null) {
        var _maxDate = moment(this.maxDate);
        var _date = moment(date);

        if (!checkTime && !checkMinute) {
          _maxDate.hour(0);
          _maxDate.minute(0);

          _date.hour(0);
          _date.minute(0);
        }

        _maxDate.second(0);
        _date.second(0);
        _maxDate.millisecond(0);
        _date.millisecond(0);

        if (!checkMinute) {
          _date.minute(0);
          _maxDate.minute(0);

          _return = (parseInt(_date.format("X")) <= parseInt(_maxDate.format("X")));
        }
        else {
          _return = (parseInt(_date.format("X")) <= parseInt(_maxDate.format("X")));
        }
      }

      return _return;
    },
    isInDisableDates: function (date) {
      var dut = date.format('MMMM Do YYYY')
      if (this.disableDates.indexOf(dut) > -1) {
        return false;
      }
      return true;
    },
    selectDate: function (date) {
      if (date) {
        this.currentDate = moment(date);
        if (!this.isAfterMinDate(this.currentDate)) {
          this.currentDate = moment(this.minDate);
        }

        if (!this.isBeforeMaxDate(this.currentDate)) {
          this.currentDate = moment(this.maxDate);
        }
        this.currentDate.locale(this.params.lang);
        this.calendarStart = moment(this.currentDate);
        this.meridien = this.currentDate.hour() >= 12 ? 'PM' : 'AM';
      }
    },
    setName: function () {
      var text = "";
      var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

      for (var i = 0; i < 5; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
      }

      return text;
    },
    isPM: function () {
      return this.meridien === 'PM';
    },
    incrementYear: function (amount) {
      if (amount === 1 && this.isNextYearVisible()) {
        this.selectDate(this.currentDate.add(amount, 'year'));
      }

      if (amount === -1 && this.isPreviousYearVisible()) {
        this.selectDate(this.currentDate.add(amount, 'year'));
      }
    },
    isPreviousMonthVisible: function () {
      return this.calendarStart && this.isAfterMinDate(moment(this.calendarStart).startOf('month'), false, false);
    },
    isNextMonthVisible: function () {
      return this.calendarStart && this.isBeforeMaxDate(moment(this.calendarStart).endOf('month'), false, false);
    },
    isPreviousYearVisible: function () {
      return this.calendarStart && this.isAfterMinDate(moment(this.calendarStart).startOf('year'), false, false);
    },
    isNextYearVisible: function () {
      return this.calendarStart && this.isBeforeMaxDate(moment(this.calendarStart).endOf('year'), false, false);
    },
    isHourAvailable: function (hour) {
      var _date = moment(this.currentDate);
      _date.hour(this.convertHours(hour)).minute(0).second(0);
      return this.isAfterMinDate(_date, true, false) && this.isBeforeMaxDate(_date, true, false);
    },
    isMinuteAvailable: function (minute) {
      var _date = moment(this.currentDate);
      _date.minute(minute).second(0);
      return this.isAfterMinDate(_date, true, true) && this.isBeforeMaxDate(_date, true, true);
    },
    start: function () {
      this.currentView = VIEW_STATES.DATE;
      //this.initDates();
      if (this.params.date) {
        this.initDate();
      } else {
        if (this.params.time) {
          this.initHours();
        }
      }
    },
    today: function () {
      this.selectDate(Date.now());
    },
    ok: function () {
      switch (this.currentView) {
        case VIEW_STATES.DATE:
          if (this.params.time === true) {
            this.initHours();
          }
          else {
            this.hide(true);
          }
          break;
        case VIEW_STATES.HOUR:
          this.initMinutes();
          break;
        case VIEW_STATES.MINUTE:
          this.hide(true);
          break;
      }
    },
    cancel: function () {
      if (this.params.time) {
        switch (this.currentView) {
          case VIEW_STATES.DATE:
            this.hide();
            break;
          case VIEW_STATES.HOUR:
            if (this.params.date) {
              this.initDate();
            }
            else {
              this.hide();
            }
            break;
          case VIEW_STATES.MINUTE:
            this.initHours();
            break;
        }
      }
      else {
        this.hide();
      }
    },
    selectMonthBefore: function () {
      this.calendarStart.subtract(1, 'months');
    },
    selectMonthAfter: function () {
      this.calendarStart.add(1, 'months');
    },
    selectYearBefore: function () {
      this.calendarStart.subtract(1, 'years');
    },
    selectYearAfter: function () {
      this.calendarStart.add(1, 'years');
    },
    selectAM: function () {
      if (this.isHourAvailable(0) || this.isHourAvailable(12)) {
        if (this.currentDate.hour() >= 12) {
          this.selectDate(this.currentDate.subtract(12, 'hours'));
        }
        if (!this.isHourAvailable(this.currentDate.hour())) {
          this.selectDate(this.currentDate.hour(this.minDate.hour()));
        }
        if (!this.isMinuteAvailable(this.currentDate.minute())) {
          this.selectDate(this.currentDate.minute(this.minDate.minute()));
        }
      }
    },
    selectPM: function () {
      if (this.isHourAvailable(13) || this.isHourAvailable(24)) {
        if (this.currentDate.hour() < 12) {
          this.selectDate(this.currentDate.add(12, 'hours'));
        }
        if (!this.isHourAvailable(this.currentDate.hour())) {
          this.selectDate(this.currentDate.hour(this.maxDate.hour()));
        }
        if (!this.isMinuteAvailable(this.currentDate.minute())) {
          this.selectDate(this.currentDate.minute(this.maxDate.minute()));
        }
      }
    },
    convertHours: function (h) {
      var _return = h;
      if ((h < 12) && this.isPM())
        _return += 12;

      return _return;
    },
    hide: function (okBtn) {
      if (okBtn) {
        this._dialog.hide(this.currentDate);
      } else {
        this._dialog.cancel();
      }
    }
  };


  angular.module(moduleName)
    .directive('mdcDatetimePickerCalendar', [
      function () {

        var YEAR_MIN = 1920,
          YEAR_MAX = new Date().getFullYear() + 30,
          MONTHS_IN_ALL = (YEAR_MAX - YEAR_MIN + 1) * 12,
          ITEM_HEIGHT = 240,
          MONTHS = [];
        for (var i = 0; i < MONTHS_IN_ALL; i++) {
          MONTHS.push(i);
        }

        var currentMonthIndex = function (date, low) {
          low = low ? low : 0;
          var year = date.year();
          var month = date.month();
          return (((year - YEAR_MIN) * 12) + month - 1 ) - low;
        };

        return {
          restrict: 'E',
          scope: {
            picker: '=',
            date: '='
          },
          bindToController: true,
          controllerAs: 'cal',
          controller: ['$scope', function ($scope) {
            this.$onInit = function () {
              var calendar = this,
                picker = this.picker,
                days = [];

              for (var i = picker.params.weekStart; days.length < 7; i++) {
                if (i > 6) {
                  i = 0;
                }
                days.push(i.toString());
              }

              calendar.week = days;
              if (!picker.maxDate && !picker.minDate) {
                calendar.months = MONTHS;
              } else {
                var low = picker.minDate ? currentMonthIndex(picker.minDate) : 0;
                var high = picker.maxDate ? (currentMonthIndex(picker.maxDate) + 1) : MONTHS_IN_ALL;
                calendar.months = MONTHS.slice(low, high);
              }


              calendar.getItemAtIndex = function (index) {
                var month = ((index + 1) % 12) || 12;
                var year = YEAR_MIN + Math.floor(index / 12);
                var monthObj = moment(picker.currentDate)
                  .year(year)
                  .month(month);
                return generateMonthCalendar(monthObj);
              };

              calendar.topIndex = currentMonthIndex(picker.currentDate) - calendar.months[0];

              $scope.$watch(function () {
                return picker.currentDate ? picker.currentDate.format('YYYY-MM') : '';
              }, function (val2, val1) {
                if (val2 != val1) {
                  var nDate = moment(val2, 'YYYY-MM');
                  var low = picker.minDate ? currentMonthIndex(picker.minDate) : 0;
                  var index = currentMonthIndex(nDate, low);
                  if (calendar.topIndex != index) {
                    calendar.topIndex = index;
                  }
                }
              });

              var generateMonthCalendar = function (date) {
                var month = {};
                if (date !== null) {
                  month.name = date.format('MMMM YYYY');
                  var startOfMonth = moment(date).locale(picker.params.lang).startOf('month')
                      .hour(date.hour())
                      .minute(date.minute())
                    ;
                  var iNumDay = startOfMonth.format('d');
                  month.days = [];
                  for (var i = startOfMonth.date(); i <= startOfMonth.daysInMonth(); i++) {
                    if (i === startOfMonth.date()) {
                      var iWeek = calendar.week.indexOf(iNumDay.toString());
                      if (iWeek > 0) {
                        for (var x = 0; x < iWeek; x++) {
                          month.days.push(0);
                        }
                      }
                    }
                    month.days.push(moment(startOfMonth).locale(picker.params.lang).date(i));
                  }

                  var daysInAWeek = 7, daysTmp = [], slices = Math.ceil(month.days.length / daysInAWeek);
                  for (var j = 0; j < slices; j++) {
                    daysTmp.push(month.days.slice(j * daysInAWeek, (j + 1) * daysInAWeek));
                  }
                  month.days = daysTmp;
                  return month;
                }

              };

              calendar.toDay = function (i) {
                return moment(parseInt(i), "d")
                  .locale(picker.params.lang)
                  .format("dd")
                  .substring(0, 1);
              };

              calendar.isInRange = function (date) {
                return picker.isAfterMinDate(moment(date), false, false)
                  && picker.isBeforeMaxDate(moment(date), false, false)
                  && picker.isInDisableDates(moment(date));
              };

              calendar.selectDate = function (date) {
                if (date) {
                  if (calendar.isSelectedDay(date)) {
                    return picker.ok();
                  }
                  picker.selectDate(moment(date).hour(calendar.date.hour()).minute(calendar.date.minute()));
                }
              };

              calendar.isSelectedDay = function (m) {
                return m && calendar.date.date() === m.date() && calendar.date.month() === m.month() && calendar.date.year() === m.year();
              };

              calendar.isDateOfTheDay = function (m) {
                var today = calendar.picker.options.showTodaysDate;
                if (!today) {
                  return false;
                }

                return m && today.date() === m.date() && today.month() === m.month() && today.year() === m.year();
              }
            }
          }],
          template: '<md-virtual-repeat-container md-top-index="cal.topIndex" class="months">' +
          '<div md-virtual-repeat="idx in ::cal.months" md-auto-shrink md-item-size="' + ITEM_HEIGHT + '">' +
          '     <div mdc-datetime-picker-calendar-month idx="idx"></div>' +
          '</div>' +
          '</md-virtual-repeat-container>'
        };
      }])
    .directive('mdcDatetimePickerCalendarMonth', ['$compile',
      function ($compile) {
        var buildCalendarContent = function (element, scope) {
          var tbody = angular.element(element[0].querySelector('tbody'));
          var calendar = scope.cal, month = scope.month;

          var tbodyHtml = [];

          month.days.forEach(function (weekDays, i) {
            tbodyHtml.push('<tr>');
            weekDays.forEach(function (weekDay, j) {
              tbodyHtml.push('<td>');
              if (weekDay) {
                if (calendar.isInRange(weekDay)) {
                  //build a
                  var scopeRef = 'month[\'days\'][' + i + '][' + j + ']';

                  tbodyHtml.push('<a href="#" mdc-dtp-noclick class="dtp-select-day" ng-class="{selected: cal.isSelectedDay(' + scopeRef + '), hilite: cal.isDateOfTheDay(' + scopeRef + ')}" ng-click="cal.selectDate(' + scopeRef + ')">');
                  tbodyHtml.push(weekDay.format('D'));
                  tbodyHtml.push('</a>');
                } else {
                  tbodyHtml.push('<span class="dtp-select-day">');
                  tbodyHtml.push(weekDay.format('D'));
                  tbodyHtml.push('</span>');
                }
              }
              tbodyHtml.push('</td>');
            });
            tbodyHtml.push('</tr>');
          });

          tbody.html(tbodyHtml.join(''));
          $compile(tbody)(scope);
        };

        return {
          scope: {
            idx: '='
          },
          require: '^mdcDatetimePickerCalendar',
          restrict: 'AE',
          template: '<div class="dtp-picker-month">{{month.name}}</div>'
          + '<table class="table dtp-picker-days">'
          + '    <thead>'
          + '    <tr>'
          + '        <th ng-repeat="day in cal.week track by $index">{{cal.toDay(day)}}</th>'
          + '    </tr>'
          + '    </thead>'
          + '    <tbody>'
          + '    </tbody>'
          + '</table>',
          link: function (scope, element, attrs, calendar) {
            scope.cal = calendar;
            scope.month = calendar.getItemAtIndex(parseInt(scope.idx));
            buildCalendarContent(element, scope);
            scope.$watch(function () {
              return scope.idx;
            }, function (idx, oldIdx) {
              if (idx != oldIdx) {
                scope.month = calendar.getItemAtIndex(parseInt(scope.idx));
                buildCalendarContent(element, scope);
              }
            });
          }
        };
      }
    ])
  ;

  angular.module(moduleName)
    .directive('mdcDtpNoclick', function () {
      return {
        link: function (scope, el) {
          el.on('click', function (e) {
            e.preventDefault();
          });
        }
      };
    });
  angular.module(moduleName)
    .directive('mdcDatetimePickerClock', [
      function () {

        var template = '<div class="dtp-picker-clock"><span ng-if="!points || points.length < 1">&nbsp;</span>'
          + '<div ng-repeat="point in points" class="dtp-picker-time" ng-style="point.style">'
          + '   <a href="#" mdc-dtp-noclick ng-class="{selected: point.value===currentValue}" class="dtp-select-hour" ng-click="setTime(point.value)" ng-if="pointAvailable(point)">{{point.display}}</a>'
          + '   <a href="#" mdc-dtp-noclick class="disabled dtp-select-hour" ng-if="!pointAvailable(point)">{{point.display}}</a>'
          + '</div>'
          + '<div class="dtp-hand dtp-hour-hand"></div>'
          + '<div class="dtp-hand dtp-minute-hand"></div>'
          + '<div class="dtp-clock-center"></div>'
          + '</div>';

        return {
          restrict: 'E',
          template: template,
          link: function (scope, element, attrs) {
            var minuteMode = attrs.mode === 'minutes';
            var picker = scope.picker;
            //banking on the fact that there will only be one at a time
            var componentRoot = document.querySelector('md-dialog.dtp');
            var exec = function () {
              var clock = angular.element(element[0].querySelector('.dtp-picker-clock')),
                pickerEl = angular.element(componentRoot.querySelector('.dtp-picker'));

              var w = componentRoot.querySelector('.dtp-content').offsetWidth;
              var pl = parseInt(css(pickerEl, 'paddingLeft').replace('px', '')) || 0;
              var pr = parseInt(css(pickerEl, 'paddingRight').replace('px', '')) || 0;
              var ml = parseInt(css(clock, 'marginLeft').replace('px', '')) || 0;
              var mr = parseInt(css(clock, 'marginRight').replace('px', '')) || 0;
              //set width
              var clockWidth = (w - (ml + mr + pl + pr));
              clock.css('width', (clockWidth) + 'px');

              var pL = parseInt(css(pickerEl, 'paddingLeft').replace('px', '')) || 0;
              var pT = parseInt(css(pickerEl, 'paddingTop').replace('px', '')) || 0;
              var mL = parseInt(css(clock, 'marginLeft').replace('px', '')) || 0;
              var mT = parseInt(css(clock, 'marginTop').replace('px', '')) || 0;

              var r = (clockWidth / 2);
              var j = r / 1.2; //???

              var points = [];

              for (var h = 0; h < 12; ++h) {
                var x = j * Math.sin(Math.PI * 2 * (h / 12));
                var y = j * Math.cos(Math.PI * 2 * (h / 12));
                var left = (r + x + pL / 2) - (pL + mL);
                var top = (r - y - mT / 2) - (pT + mT);

                var hour = {
                  left: left,
                  top: top,
                  value: (minuteMode ? (h * 5) : h), //5 for minute 60/12
                  style: {'margin-left': left + 'px', 'margin-top': top + 'px'}
                };

                if (minuteMode) {
                  hour.display = hour.value < 10 ? ('0' + hour.value) : hour.value;
                } else {

                  if (picker.params.shortTime) {
                    hour.display = (h === 0) ? 12 : h;
                  } else {
                    hour.display = picker.isPM() ? h + 12 : h;
                  }
                }


                points.push(hour);
              }

              scope.points = points;
              setCurrentValue();
              clock.css('height', clockWidth + 'px');
              //picker.initHands(true);

              var clockCenter = element[0].querySelector('.dtp-clock-center');
              var centerWidth = (clockCenter.offsetWidth / 2) || 7.5,
                centerHeight = (clockCenter.offsetHeight / 2) || 7.5;
              var _hL = r / 1.8;
              var _mL = r / 1.5;

              angular.element(element[0].querySelector('.dtp-hour-hand')).css({
                left: r + (mL * 1.5) + 'px',
                height: _hL + 'px',
                marginTop: (r - _hL - pL) + 'px'
              }).addClass(!minuteMode ? 'on' : '');

              angular.element(element[0].querySelector('.dtp-minute-hand')).css
              ({
                left: r + (mL * 1.5) + 'px',
                height: _mL + 'px',
                marginTop: (r - _mL - pL) + 'px'
              }).addClass(minuteMode ? 'on' : '');

              angular.element(clockCenter).css({
                left: (r + pL + mL - centerWidth) + 'px',
                marginTop: (r - (mL / 2)) - centerHeight + 'px'
              });
              animateHands();
            };

            var animateHands = function () {
              var _date = picker.currentNearest5Minute();
              var h = _date.hour();
              var m = _date.minute();

              rotateElement(angular.element(element[0].querySelector('.dtp-hour-hand')), (360 / 12) * h);
              var mdg = ((360 / 60) * (5 * Math.round(m / 5)));
              rotateElement(angular.element(element[0].querySelector('.dtp-minute-hand')), mdg);
            };

            var rotateElement = function (el, deg) {
              angular.element(el).css({
                WebkitTransform: 'rotate(' + deg + 'deg)',
                '-moz-transform': 'rotate(' + deg + 'deg)',
                '-ms-transform': 'rotate(' + deg + 'deg)',
                'transform': 'rotate(' + deg + 'deg)'
              });
            };


            var setCurrentValue = function () {
              var date = picker.currentNearest5Minute();
              scope.currentValue = minuteMode ? date.minute() : (date.hour() % 12);
            };

            scope.$watch(function () {
              var tmp = picker.currentNearest5Minute();
              return tmp ? tmp.format('HH:mm') : '';
            }, function () {
              setCurrentValue();
              animateHands();
            });


            var setDisplayPoints = function (isPM, points) {
              for (var i = 0; i < points.length; i++) {
                points[i].display = i;
                if (isPM) {
                  points[i].display += 12;
                }
              }
              return points;
            };

            if (!picker.params.shortTime) {
              scope.$watch('picker.meridien', function () {
                if (!minuteMode) {
                  if (scope.points) {
                    var points = setDisplayPoints(picker.isPM(), angular.copy(scope.points));
                    scope.points = points;
                  }
                }
              });
            }


            scope.setTime = function (val) {
              if (val === scope.currentValue) {
                picker.ok();
              }

              if (!minuteMode) {
                picker.currentDate.hour(picker.isPM() ? (val + 12) : val);
              } else {
                picker.currentDate.minute(val);
              }
              picker.currentDate.second(0)
            };

            scope.pointAvailable = function (point) {
              return minuteMode ? picker.isMinuteAvailable(point.value) : picker.isHourAvailable(point.value);
            };

            var unWatcher = scope.$watch(function () {
              return element[0].querySelectorAll('div').length;
            }, function () {
              exec();
              unWatcher();
            });
          }
        }
      }]);

})();

/* (function () {
    "use strict";
    angular.module("su-fiscalizacao").directive("incLocalizacaoImovel", directive);

    function directive($rootScope, TipoImovelService, Dominio, CidadesExtService, ectCepService, NotificadorServiceFiscalizacao, MAPA_CONSTANTS_FISCALIZACAO, LogradouroService, tabsIncorporacao, CartografiaServiceFiscalizacao, MensagensService) {
        return {
            templateUrl: "scripts-fiscalizacao/app/directives/secoes-formulario/localizacao/templates/inc-localizacao-imovel.html",
            scope: {
                imovel: "=",
                action: "=",
                edificacoesUnidadeAutonoma: "=?"
            },
            link: function ($scope) {

                var vm = $scope,
                    notifiers = MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES;

                vm.usuario = $rootScope.usuarioLogado || { permissoes: [], jurisdicoes: [] };
                vm.tiposImoveis = [];
                vm.paises = [];
                vm.cidadesExteriores = [];
                vm.tiposLogradouro = [];
                vm.isCepGenerico = false;
                vm.cidadeExterior = null;
                vm.imovelOriginal = {};

                $scope.$watch("usuarioLogado",

                    function(){
                        if($rootScope.usuarioLogado){
                            vm.usuario = $rootScope.usuarioLogado;
                            vm.getProprietariosCadastro();
                        }
                    }
                );

                function init() {
                    TipoImovelService.query().$promise.then(function (result) {
                        vm.tiposImoveis = result;
                    });
                    LogradouroService.query().$promise.then(function (result) {
                        vm.tiposLogradouro = result;
                    });
                    vm.paises = Dominio.Constantes.Paises;

                    if (isEdicao()) {
                        vm.imovelOriginal = angular.copy(vm.imovel);
                    }
                }

                vm.isTipoUnidadeAutonoma = function (tipoImovel) {
                    return tipoImovel.tipoPai === "UNIDADE AUTÔNOMA";
                };

                vm.isNotTipoUnidadeAutonomaPrimeiraParte = function (tipoImovel) {
                    return !vm.isTipoUnidadeAutonoma(tipoImovel) && tipoImovel.ordem <= 2;
                };

                vm.isNotTipoUnidadeAutonomaSegundaParte = function (tipoImovel) {
                    return !vm.isTipoUnidadeAutonoma(tipoImovel) && tipoImovel.ordem >= 6;
                };

                vm.getProprietariosCadastro = function() {
                    return vm.usuario.organizacao + (possuiPermissaoCadastroUniao() ? ' e UNIÃO' : '');
                };

                vm.buscaCidadesPorPais = function () {
                    if (vm.imovel.enderecamento.pais && !vm.isPaisBrasil()) {
                        vm.cidadesExteriores = CidadesExtService.query({id_pais: vm.imovel.enderecamento.pais.id});
                    }
                };

                vm.isPaisBrasil = function() {
                    return vm.imovel.enderecamento.pais.id === 1;
                };

                vm.isImovelEspelhoDagua = function() {
                    return vm.imovel.tipoImovel && vm.imovel.tipoImovel.id === 3;
                };

                vm.buscaEnderecoPorCep = function() {
                    var cep = vm.imovel.enderecamento.cep;
                    if (cep && cep.length === 8) {
                        ectCepService.get({cep: cep}).$promise.then(function(data) {
                            if (isEdicao() && (data.uf !== vm.imovel.enderecamento.uf || data.municipio !== vm.imovel.enderecamento.municipio)) {
                                vm.imovel.enderecamento = vm.imovelOriginal.enderecamento;
                                MensagensService.exibirMensagemErro("Este tipo de alteração não é permitido. Se ela for mesmo necessária, realize o cancelamento e inclua um novo imóvel.");
                            } else {
                                if (possuiPermissaoNaJurisdicao(data.uf)) {
                                    data.pais = Dominio.Constantes.Brasil;
                                    vm.imovel.enderecamento = data;
                                    vm.isCepGenerico = data.stLocalidade == 0;
                                    CartografiaServiceFiscalizacao.buscarGeometria(vm.imovel.enderecamento).then(
                                        function (result) {
                                            var geometria = result.data ? result.data : data.geometria;
                                            NotificadorServiceFiscalizacao.notify($scope, geometria, notifiers.CENTRALIZAR_POR_MUNICIPIO);
                                        }
                                    );
                                } else {
                                    vm.isCepGenerico = false;
                                    vm.imovel.enderecamento = {};
                                    vm.imovel.enderecamento.pais = Dominio.Constantes.Brasil;
                                    MensagensService.exibirMensagemErro("Você não possui permissão para inserir imóveis nesta UF.");
                                }
                            }
                        }, function(){
                            vm.isCepGenerico = false;
                            vm.imovel.enderecamento.cep = "";
                            MensagensService.exibirMensagemErro("CEP não encontrado");
                        });
                    } else {
                        vm.isCepGenerico = false;
                        vm.imovel.enderecamento = {};
                        vm.imovel.enderecamento.pais = Dominio.Constantes.Brasil;
                    }
                };

                vm.atualizaCidadeExterior = function () {
                    vm.imovel.enderecamento.cidadeExterior = vm.cidadeExterior === "Outro" ? null : vm.cidadeExterior;
                };

                vm.atualizaTipoImovel = function () {
                    tabsIncorporacao.atualizaTabs(vm.imovel.tipoImovel);
                };

                vm.isTipoImovelDisabled = function () {
                    return isDetalhamento();
                };

                vm.isPaisDisabled = function () {
                    return !isCadastro();
                };

                vm.isCepDisabled = function () {
                    return isDetalhamento();
                };

                vm.isTipoLogradouroDisabled = function () {
                    return isDetalhamento() || !vm.imovel.enderecamento.cep || (vm.imovel.enderecamento.cep && (!vm.isCepGenerico || vm.imovel.enderecamento.tipoLogradouro));
                };

                vm.isLogradouroDisabled = function () {
                    return isDetalhamento() || !vm.imovel.enderecamento.cep || (vm.imovel.enderecamento.cep && (!vm.isCepGenerico || vm.imovel.enderecamento.logradouro));
                };

                vm.isNumeroDisabled = function () {
                    return isDetalhamento();
                };

                vm.isBairroDisabled = function () {
                    return isDetalhamento() || !vm.imovel.enderecamento.cep || (vm.imovel.enderecamento.cep && (!vm.isCepGenerico || vm.imovel.enderecamento.bairro));
                };

                vm.isCidadeExteriorDisabled = function () {
                    return !isCadastro();
                };

                vm.isCodigoPostalDisabled = function () {
                    return isDetalhamento();
                };

                vm.isComplementoDisabled = function () {
                    return isDetalhamento();
                };

                vm.isEnderecoDisabled = function () {
                    return isDetalhamento();
                };

                vm.isDadoAlterado = function (propAlterada) {
                    if (isEdicao()) {
                        var propOriginal = propAlterada.replace("imovel", "imovelOriginal"),
                            arrayPropAlterada = propAlterada.split("."),
                            arrayPropOriginal = propOriginal.split("."),
                            dadoAlterado = vm,
                            dadoOriginal = vm,
                            i;

                        for (i = 0; i < arrayPropAlterada.length; i++) {
                            if (angular.isDefined(dadoAlterado) && dadoAlterado !== null) {
                                dadoAlterado = dadoAlterado[arrayPropAlterada[i]];
                            }
                        }
                        for (i = 0; i < arrayPropOriginal.length; i++) {
                            if (angular.isDefined(dadoOriginal) && dadoOriginal !== null) {
                                dadoOriginal = dadoOriginal[arrayPropOriginal[i]];
                            }
                        }

                        return dadoOriginal != dadoAlterado ? "dado-alterado" : "";
                    } else {
                        return "";
                    }
                };

                function possuiPermissaoCadastroUniao() {
                    return vm.usuario.permissoes.includes("CAD_IMOVEL.CADASTRAR_IMOVEL_UNIAO");
                }

                function possuiPermissaoNaJurisdicao(uf) {
                    return vm.usuario.jurisdicoes.includes(uf);
                }

                function isCadastro() {
                    return vm.action === "add";
                }

                function isEdicao() {
                    return vm.action === "edit";
                }

                function isDetalhamento() {
                    return vm.action === "detail";
                }

                init();
            }
        };
    }
})();
 */
(function () {
    'use strict';
    incInformarCoordenadasFiscalizacao.$inject = ["SistemaReferenciaServiceFiscalizacao", "FusoUTMServiceFiscalizacao", "CartografiaServiceFiscalizacao", "NotificadorServiceFiscalizacao", "MAPA_CONSTANTS_FISCALIZACAO", "ComumFactory"];
    angular.module('su-fiscalizacao')
    .directive('incInformarCoordenadasFiscalizacao', incInformarCoordenadasFiscalizacao);

    function incInformarCoordenadasFiscalizacao(SistemaReferenciaServiceFiscalizacao, FusoUTMServiceFiscalizacao, CartografiaServiceFiscalizacao, NotificadorServiceFiscalizacao, MAPA_CONSTANTS_FISCALIZACAO, ComumFactory) {
        return {
            retrict: 'E',
            templateUrl: 'scripts-fiscalizacao/app/directives/localizacao/templates/inc-informar-coordenadas-fiscalizacao.html',
            scope: {
                apenasLeitura: '=',
                latitude: '=',
                longitude: '=',
                codigoSistemaReferencia: '=',
                formatoCoordenadas: '=',
                sistemaReferencia: '=?'
            },
            link: function ($scope) {

                var vm = $scope,
                    notifiers = MAPA_CONSTANTS_FISCALIZACAO.NOTIFICADORES,
                    WGS84_WEB_MERCATOR = 3857;

                vm.imovel = {};

                vm.sistemasReferencia = [];
                vm.sistemasReferenciaSelected = {};
                vm.fusosUTM = [];
                vm.grauMinutoSegundo = null;
                vm.grauDecimal = null;
                vm.metricas = null;
                vm.submittedCoodenadas = false;

                function init() {
                    SistemaReferenciaServiceFiscalizacao.buscarTodos().then(function (result) {
                        vm.sistemasReferencia = result.data;
                        for(var i =0; i< result.data.length; i++){
                            if (!vm.codigoSistemaReferencia && result.data[i].codigo === '4674') {
                                vm.codigoSistemaReferencia = result.data[i].codigo;
                            }
                        }

                        if(!vm.formatoCoordenadas){
                            vm.formatoCoordenadas = 'GMS'
                        }

                        if (vm.latitude && vm.longitude && vm.codigoSistemaReferencia) {
                            atualizarInformacoesCoordenadas([vm.latitude, vm.longitude]);
                        }
                    });

                    FusoUTMServiceFiscalizacao.buscarTodos().then(function (result) {
                        vm.fusosUTM = result.data;
                    });

                    vm.grauMinutoSegundo = novoGMS();
                    vm.grauDecimal = novoGD();
                    vm.metricas = novoM();
                }

                function novoGMS() {
                    return {
                        latitude: {
                            hemisferio: null,
                            graus: null,
                            minutos: null,
                            segundos: null
                        },
                        longitude: {
                            hemisferio: null,
                            graus: null,
                            minutos: null,
                            segundos: null
                        }
                    };
                }

                function novoGD() {
                    return {
                        latitude: {
                            hemisferio: null,
                            decimal: null
                        },
                        longitude: {
                            hemisferio: null,
                            decimal: null
                        }
                    };
                }

                function novoM() {
                    return {
                        coordenadaE: null,
                        coordenadaN: null,
                        fusoUTM: null
                    };
                }

                function atualizarInformacoesCoordenadas(coordenadas) {
                    CartografiaServiceFiscalizacao.transformarGeometria(WGS84_WEB_MERCATOR, vm.codigoSistemaReferencia, coordenadas[0], coordenadas[1]).then(
                        function (result) {
                            vm.grauDecimal.longitude.decimal = result.data.coordinates[0];
                            vm.grauDecimal.longitude.hemisferio = vm.grauDecimal.longitude.decimal < 0 ? 'O' : 'L';

                            vm.grauDecimal.latitude.decimal = result.data.coordinates[1];
                            vm.grauDecimal.latitude.hemisferio = vm.grauDecimal.latitude.decimal < 0 ? 'S' : 'N';

                            vm.grauMinutoSegundo = CartografiaServiceFiscalizacao.gdToGms(vm.grauDecimal);
                            vm.metricas = CartografiaServiceFiscalizacao.gdToUtm(vm.grauDecimal);
                        }
                    );
                }

                NotificadorServiceFiscalizacao.subscribe(vm, function (evento, escopo, coordenadas) {
                    if (vm.possuiSistemaReferencia()) {
                        atualizarInformacoesCoordenadas(coordenadas);
                    }
                }, notifiers.ATUALIZAR_COORDENADAS_DO_PONTO);

                NotificadorServiceFiscalizacao.subscribe(vm, function () {
                    vm.grauMinutoSegundo = novoGMS();
                    vm.grauDecimal = novoGD();
                    vm.metricas = novoM();
                }, notifiers.REMOVER_COORDENADAS_DO_PONTO);

                vm.possuiSistemaReferencia = function () {
                    if(!vm.validaValor(vm.codigoSistemaReferencia)) {
                        for(var i =0; i< vm.sistemasReferencia.length; i++){
                            if (!vm.codigoSistemaReferencia && vm.sistemasReferencia[i].codigo === '4674') {
                                vm.codigoSistemaReferencia = vm.sistemasReferencia[i].codigo;
                            }
                        }
                    }

                    if(!vm.formatoCoordenadas){
                        vm.formatoCoordenadas = 'GMS'
                    }

                    return true;
                };

                vm.onChangeHemisferioLatitude = function () {
                    if (vm.grauDecimal.latitude.hemisferio === 'S') {
                        if (vm.grauDecimal.latitude.decimal && parseFloat(vm.grauDecimal.latitude.decimal) !== 0) {
                            vm.grauDecimal.latitude.decimal = Math.abs(vm.grauDecimal.latitude.decimal) * -1;
                        }
                    } else if (vm.grauDecimal.latitude.hemisferio === 'N') {
                        vm.grauDecimal.latitude.decimal = Math.abs(vm.grauDecimal.latitude.decimal);
                    }
                };

                vm.onChangeHemisferioLongitude = function () {
                    if (vm.grauDecimal.longitude.hemisferio === 'O') {
                        if (vm.grauDecimal.longitude.decimal && parseFloat(vm.grauDecimal.longitude.decimal) !== 0) {
                            vm.grauDecimal.longitude.decimal = Math.abs(vm.grauDecimal.longitude.decimal) * -1;
                        }
                    } else if (vm.grauDecimal.longitude.hemisferio === 'L') {
                        vm.grauDecimal.longitude.decimal = Math.abs(vm.grauDecimal.longitude.decimal);
                    }
                };

                vm.marcarPonto = function () {
                    if (validaFormulario()) {
                        if (vm.formatoCoordenadas === 'M') {
                            vm.grauDecimal = CartografiaServiceFiscalizacao.utmToGd(vm.metricas);
                            vm.grauMinutoSegundo = CartografiaServiceFiscalizacao.gdToGms(vm.grauDecimal);
                        } else if (vm.formatoCoordenadas === 'GMS') {
                            vm.grauDecimal = CartografiaServiceFiscalizacao.gmsToGd(vm.grauMinutoSegundo);
                            vm.metricas = CartografiaServiceFiscalizacao.gdToUtm(vm.grauDecimal);
                        } else {
                            vm.metricas = CartografiaServiceFiscalizacao.gdToUtm(vm.grauDecimal);
                            vm.grauMinutoSegundo = CartografiaServiceFiscalizacao.gdToGms(vm.grauDecimal);
                        }

                        CartografiaServiceFiscalizacao.transformarGeometria(vm.codigoSistemaReferencia, WGS84_WEB_MERCATOR, vm.grauDecimal.longitude.decimal, vm.grauDecimal.latitude.decimal).then(
                            function (result) {
                                NotificadorServiceFiscalizacao.notify(vm, result.data, notifiers.MARCAR_PONTO_POR_COORDENADAS);
                            }
                        );
                    } else {
                        vm.submittedCoodenadas = true;
                        ComumFactory.mostrarMensagemError('Campo(s) obrigatório(s) não preenchido(s)');
                    }
                };

                function validaFormulario() {
                    switch (vm.formatoCoordenadas) {
                        case 'GMS':
                            return vm.validaValor(vm.grauMinutoSegundo.latitude.hemisferio) && vm.validaValor(vm.grauMinutoSegundo.latitude.graus) && vm.validaValor(vm.grauMinutoSegundo.latitude.minutos) && vm.validaValor(vm.grauMinutoSegundo.latitude.segundos)
                                && vm.validaValor(vm.grauMinutoSegundo.longitude.hemisferio) && vm.validaValor(vm.grauMinutoSegundo.longitude.graus) && vm.validaValor(vm.grauMinutoSegundo.longitude.minutos) && vm.validaValor(vm.grauMinutoSegundo.longitude.segundos);

                        case 'GD':
                            return vm.validaValor(vm.grauDecimal.latitude.hemisferio) && vm.validaValor(vm.grauDecimal.latitude.decimal) && vm.validaValor(vm.grauDecimal.longitude.hemisferio) && vm.validaValor(vm.grauDecimal.longitude.decimal);

                        case 'M':
                            return vm.validaValor(vm.metricas.coordenadaE) && vm.validaValor(vm.metricas.coordenadaN) && vm.validaValor(vm.metricas.fusoUTM);

                        default:
                            return false;
                    }
                }

                vm.validaValor = function (valor) {
                    return valor !== '' && valor !== null && valor !== undefined;
                };

                vm.isSistemaReferenciaDisabled = function () {
                    return isDetalhamento();
                };

                vm.handleItemSelected = function (sistemaReferencia) {
                    for(var i = 0; i < vm.sistemasReferencia; i++){
                        if(vm.sistemasReferencia[i].name == sistemaReferencia){
                            vm.codigoSistemaReferencia = vm.sistemasReferencia[i].codigo;
                        }
                    }
                };

                vm.isGrauDecimalDisabled = function () {
                    return isDetalhamento();
                };

                vm.isMetricasDisabled = function () {
                    return isDetalhamento();
                };

                vm.isGrauMinutoSegundoDisabled = function () {
                    return isDetalhamento();
                };

                vm.esconderBotaoMarcarPonto = function () {
                    return isDetalhamento();
                };

                function isDetalhamento() {
                    return vm.apenasLeitura;
                }

                init();
            }
        };
    }
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao')
        .factory('CartografiaServiceFiscalizacao', ["$http", function ($http) {

            function transformarGeometria(sistemaReferenciaEntrada, sistemaReferenciaSaida, coordenadaX, coordenadaY) {
                var parametros = {
                    sistemaReferenciaEntrada: sistemaReferenciaEntrada,
                    sistemaReferenciaSaida: sistemaReferenciaSaida,
                    geometriaPonto: {
                        type: 'Point',
                        coordinates: [
                            coordenadaX,
                            coordenadaY
                        ]
                    }
                };
                return $http.post('/fiscalizacao/api/integracoes/cartografia/geometria/transforma', parametros);
            }

            function gdToUtm(gd) {
                var lat = parseFloat(gd.latitude.decimal);
                var lng = parseFloat(gd.longitude.decimal);
                var k0 = 0.9996; //scale on central meridian
                var a = 6378137.0; //equatorial radius, meters.
                var f = 0.00335281066; //polar flattening.
                var b = a * (1 - f); //polar axis.
                var e = Math.sqrt(1 - (b / a) * (b / a)); //eccentricity
                var drad = Math.PI / 180; //Convert degrees to radians

                var phi = lat * drad; //Convert latitude to radians
                var utmz = 1 + Math.floor((lng + 180) / 6); //calculate utm zone
                var zcm = 3 + 6 * (utmz - 1) - 180; //Central meridian of zone

                // Calculate Intermediate Terms
                var esq = (1 - (b / a) * (b / a)); //e squared for use in expansions
                var e0sq = e * e / (1 - e * e); // e0 squared - always even powers
                var N = a / Math.sqrt(1 - Math.pow(e * Math.sin(phi), 2));
                var T = Math.pow(Math.tan(phi), 2);
                var C = e0sq * Math.pow(Math.cos(phi), 2);
                var A = (lng - zcm) * drad * Math.cos(phi);

                // Calculate M
                var M = phi * (1 - esq * (1 / 4 + esq * (3 / 64 + 5 * esq / 256)));
                M = M - Math.sin(2 * phi) * (esq * (3 / 8 + esq * (3 / 32 + 45 * esq / 1024)));
                M = M + Math.sin(4 * phi) * (esq * esq * (15 / 256 + esq * 45 / 1024));
                M = M - Math.sin(6 * phi) * (esq * esq * esq * (35 / 3072));
                M = M * a; //Arc length along standard meridian

                // Calculate UTM Values
                var x = (k0 * N * A * (1 + A * A * ((1 - T + C) / 6 + A * A * (5 - 18 * T + T * T + 72 * C - 58 * e0sq) / 120))) + 500000;
                var y = k0 * (M + N * Math.tan(phi) * (A * A * (1 / 2 + A * A * ((5 - T + 9 * C + 4 * C * C) / 24 + A * A * (61 - 58 * T + T * T + 600 * C - 330 * e0sq) / 720))));

                if (y < 0) {
                    y = 10000000 + y;
                }

                return {
                    coordenadaE: x,
                    coordenadaN: y,
                    fusoUTM: {
                        zona: utmz,
                        hemisferio: phi < 0 ? 'S' : 'N'
                    }
                };
            }

            function utmToGd(m) {
                var easting = m.coordenadaE;
                var northing = m.coordenadaN;
                var zone = m.fusoUTM.zona;
                var northernHemisphere = m.fusoUTM.hemisferio === 'N';

                if (!northernHemisphere) {
                    northing = 10000000 - northing;
                }

                var a = 6378137;
                var e = 0.081819191;
                var e1sq = 0.006739497;
                var k0 = 0.9996;

                var arc = northing / k0;
                var mu = arc / (a * (1 - Math.pow(e, 2) / 4.0 - 3 * Math.pow(e, 4) / 64.0 - 5 * Math.pow(e, 6) / 256.0));

                var ei = (1 - Math.pow((1 - e * e), (1 / 2.0))) / (1 + Math.pow((1 - e * e), (1 / 2.0)));

                var ca = 3 * ei / 2 - 27 * Math.pow(ei, 3) / 32.0;

                var cb = 21 * Math.pow(ei, 2) / 16 - 55 * Math.pow(ei, 4) / 32;
                var cc = 151 * Math.pow(ei, 3) / 96;
                var cd = 1097 * Math.pow(ei, 4) / 512;
                var phi1 = mu + ca * Math.sin(2 * mu) + cb * Math.sin(4 * mu) + cc * Math.sin(6 * mu) + cd * Math.sin(8 * mu);

                var n0 = a / Math.pow((1 - Math.pow((e * Math.sin(phi1)), 2)), (1 / 2.0));

                var r0 = a * (1 - e * e) / Math.pow((1 - Math.pow((e * Math.sin(phi1)), 2)), (3 / 2.0));
                var fact1 = n0 * Math.tan(phi1) / r0;

                var _a1 = 500000 - easting;
                var dd0 = _a1 / (n0 * k0);
                var fact2 = dd0 * dd0 / 2;

                var t0 = Math.pow(Math.tan(phi1), 2);
                var Q0 = e1sq * Math.pow(Math.cos(phi1), 2);
                var fact3 = (5 + 3 * t0 + 10 * Q0 - 4 * Q0 * Q0 - 9 * e1sq) * Math.pow(dd0, 4) / 24;

                var fact4 = (61 + 90 * t0 + 298 * Q0 + 45 * t0 * t0 - 252 * e1sq - 3 * Q0 * Q0) * Math.pow(dd0, 6) / 720;

                var lof1 = _a1 / (n0 * k0);
                var lof2 = (1 + 2 * t0 + Q0) * Math.pow(dd0, 3) / 6.0;
                var lof3 = (5 - 2 * Q0 + 28 * t0 - 3 * Math.pow(Q0, 2) + 8 * e1sq + 24 * Math.pow(t0, 2)) * Math.pow(dd0, 5) / 120;
                var _a2 = (lof1 - lof2 + lof3) / Math.cos(phi1);
                var _a3 = _a2 * 180 / Math.PI;

                var latitude = 180 * (phi1 - fact1 * (fact2 + fact3 + fact4)) / Math.PI;

                if (!northernHemisphere) {
                    latitude = -latitude;
                }

                var longitude = ((zone > 0) && (6 * zone - 183.0) || 3.0) - _a3;

                return {
                    latitude: {
                        hemisferio: latitude < 0 ? 'S' : 'N',
                        decimal: latitude
                    },
                    longitude: {
                        hemisferio: longitude < 0 ? 'O' : 'L',
                        decimal: longitude
                    }
                };
            }

            function gmsToGd(gms) {
                var latitude = parseFloat(gms.latitude.graus) + parseFloat(gms.latitude.minutos / 60 + gms.latitude.segundos / 3600);
                var longitude = parseFloat(gms.longitude.graus) + parseFloat(gms.longitude.minutos / 60 + gms.longitude.segundos / 3600);

                if (gms.latitude.hemisferio === 'S') {
                    latitude *= -1;
                }

                if (gms.longitude.hemisferio === 'O') {
                    longitude *= -1;
                }

                return {
                    latitude: {
                        hemisferio: gms.latitude.hemisferio,
                        decimal: latitude
                    },
                    longitude: {
                        hemisferio: gms.longitude.hemisferio,
                        decimal: longitude
                    }
                };
            }

            function gdToGms(gd) {
                var obj = {
                    latitude: {
                        hemisferio: null,
                        graus: null,
                        minutos: null,
                        segundos: null
                    },
                    longitude: {
                        hemisferio: null,
                        graus: null,
                        minutos: null,
                        segundos: null
                    }
                };

                var latitude = Math.abs(gd.latitude.decimal);
                var longitude = Math.abs(gd.longitude.decimal);

                obj.latitude.graus = parseInt(latitude);
                obj.latitude.minutos = parseInt((latitude - obj.latitude.graus) * 60);
                obj.latitude.segundos = (latitude - obj.latitude.graus - (obj.latitude.minutos / 60)) * 3600;
                obj.latitude.hemisferio = gd.latitude.hemisferio;

                obj.longitude.graus = parseInt(longitude);
                obj.longitude.minutos = parseInt((longitude - obj.longitude.graus) * 60);
                obj.longitude.segundos = (longitude - obj.longitude.graus - (obj.longitude.minutos / 60)) * 3600;
                obj.longitude.hemisferio = gd.longitude.hemisferio;

                return obj;
            }

            function buscarGeometria(endereco) {
                var enderecamentoImovel = {
                    uf: endereco.uf,
                    municipio: endereco.municipio,
                    bairro: endereco.bairro
                };

                return $http.post("/fiscalizacao/api/integracoes/cartografia/geometria/por-endereco", enderecamentoImovel);
            }

            return {
                buscarGeometria: buscarGeometria,
                transformarGeometria: transformarGeometria,
                utmToGd: utmToGd,
                gdToUtm: gdToUtm,
                gmsToGd: gmsToGd,
                gdToGms: gdToGms
            };
        }])
        .factory('SistemaReferenciaServiceFiscalizacao', ["$http", function ($http) {
            function buscarTodos() {
                return $http.get('/fiscalizacao/api/integracoes/sistemas-referencia');
            }

            return {
                buscarTodos: buscarTodos
            };
        }])
        .factory('FusoUTMServiceFiscalizacao', ["$http", function ($http) {
            function buscarTodos() {
                return $http.get('/fiscalizacao/api/integracoes/fusos-utm');
            }

            return {
                buscarTodos: buscarTodos
            };
        }]);
})();

( function () {
    "use strict";
    angular.module("su-fiscalizacao").directive("isolateForm", directive);

    function directive() {
        return {
            restrict: 'A',
            require: '?form',
            link: function (scope, elm, attrs, ctrl) {
                if (!ctrl) {
                    return;
                }

                var parentForm = ctrl.$$parentForm;
                if (!parentForm) {
                    return;
                }

                parentForm.$removeControl(ctrl);
            }
        };
    }
})();

(function () {
    'use strict';
    /*global angular*/
    angular.module('ciMapaDirectiveFiscalizacao')
        .constant('MAPA_CONSTANTS_FISCALIZACAO', {
            NOTIFICADORES: {
				CENTRALIZAR: 'centralizar',
				CENTRALIZAR_POR_MUNICIPIO: 'centralizarPorMunicipio',
                ALTERAR_VISIBILIDADE_BING: 'alterarVisibilidadeBing',
                PONTO_MARCADO: 'pontoMarcado',
                FEATURE_EXCLUIDA: 'featureExcluida',
                TERRENO_DESENHADO: 'terrenoDesenhado',
                UPLOAD_SHAPEFILE: 'uploadShapefileciMapaDirectiveFiscalizacao',
                UPLOAD_SHAPEFILE_BENFEITORIA: 'uploadShapefileBenfeitoriaciMapaDirectiveFiscalizacao',
                UPLOAD_SHAPEFILE_UNIDADE_AUTONOMA: 'uploadShapefileUnidadeAutonomaciMapaDirectiveFiscalizacao',
                VERIFICAR_PONTO_NA_ARESTA: 'verificarPontoNaAresta',
                BENFEITORIA_DESENHADA: 'benfeitoriaDesenhadaciMapaDirectiveFiscalizacao',
                SET_ID_BENFEITORIA: 'setIdBenfeitoriaciMapaDirectiveFiscalizacao',
                DESTACAR_BENFEITORIA: 'destacarBenfeitoriaciMapaDirectiveFiscalizacao',
                BENFEITORIA_EXCLUIDA: 'benfeitoriaExcluidaciMapaDirectiveFiscalizacao',
                DESFAZER_ALTERACAO_BENFEITORIA: 'desfazerAlteracaoBenfeitoriaciMapaDirectiveFiscalizacao',
                LIMPAR_DESENHO: 'limparDesenhociMapaDirectiveFiscalizacao',
                RESULTADOPESQUISARIMOVEL: 'resultadoPesquisarImovelciMapaDirectiveFiscalizacao',
                DIRECIONAR_DETALHAR: 'direcionarDetalharciMapaDirectiveFiscalizacao',
                ADICIONAR_FEATURE_PONTO: 'adicionarFeaturePontociMapaDirectiveFiscalizacao',
                ADICIONAR_FEATURE_TERRENO: 'adicionarFeatureTerrenociMapaDirectiveFiscalizacao',
                ADICIONAR_FEATURE_BENFEITORIA: 'adicionarFeatureBenfeitoriaciMapaDirectiveFiscalizacao',
                ADICIONAR_FEATURE_UNIDADE_AUTONOMA: 'adicionarFeatureUnidadeAutonomaciMapaDirectiveFiscalizacao',
                PREENCHER_AREA_PROJECAO: 'preencherAreaProjecaociMapaDirectiveFiscalizacao',
                SET_VETORIZAR_EM_TELA: 'setVetorizarEmTelaciMapaDirectiveFiscalizacao',
                DELETESHAPEFILECARTORIAL: 'deleteShapeFileCartorialciMapaDirectiveFiscalizacao',
                DELETESHAPEFILECADASTRAL: 'deleteShapeFileCadastralciMapaDirectiveFiscalizacao',
                LIMPAR_TIPO_IMOVEL: 'limparTipoImovel',
                REMOVER_PONTO_EDIFICACOES_TERRENO_UA: 'removerPontoEdificacoesTerrrenoUnidadeAutonomaciMapaDirectiveFiscalizacao',
                PONTO_IN_UNIDADE_AUTONOMA: 'pontoInUnidadeAutonoma',
                FEATURE_EXCLUIDA_UNIDADE_AUTONOMA: 'featureExcluidaUnidadeAutonomaciMapaDirectiveFiscalizacao',
                MARCAR_PONTO_POR_COORDENADAS: 'marcarPontoPorCoordenadasciMapaDirectiveFiscalizacao',
                APAGAR_BENFEITORIA_POR_COORDENADAS: 'apagarBenfeitoriaPorCoordenadasciMapaDirectiveFiscalizacao',
                ATUALIZAR_COORDENADAS_DO_PONTO: 'atualizarCoordenadasDoPontociMapaDirectiveFiscalizacao',
                REMOVER_COORDENADAS_DO_PONTO: 'removerCoordenadasDoPontociMapaDirectiveFiscalizacao',
                REMOVER_EDIFICACOES: 'removerEdificacoesciMapaDirectiveFiscalizacao',
                MARCAR_LISTA_COORDENADAS: 'marcarListaCoordenadasciMapaDirectiveFiscalizacao',
                ADICIONAR_DEMANDAS: 'adicionarDemandaciMapaDirectiveFiscalizacao',
                REMOVER_DEMANDA: 'removerDemandaciMapaDirectiveFiscalizacao',
                MARCAR_PONTOS_SHAPE_FILE: 'marcarPontosShapeFile'
            },
            FEATURE_ID: {
                PONTO_IMOVEL: 'pontoImovel',
                TERRENO_CADASTRAL: 'terrenoCadastral',
                TERRENO_CADASTRAL_SOBREPOSTO: 'terrenoCadastralSobreposto',
                TERRENO_CARTORIAL_SOBREPOSTO: 'terrenoCartorialSobreposto',
                CENTRO_SEGMENTO: 'centroSegmento',
                TERRENO_CARTORIAL: 'terrenoCartorial',
                ACESSAO_BENFEITORIA: 'acessaoBenfeitoria',
                UNIDADE_AUTONOMA: 'unidadeAutonoma',
                TERRENO_BENFEITORIA_SOBREPOSTO: 'terrenoBenfeitoriaSobreposto',
                TERRENO_BENFEITORIA_INVALIDO: 'terrenoBenfeitoriaInvalido',
                SHAPE_FILE: 'shapeFile'
            },
            GEOMETRY: {
                GEOMETRIA_INICIAL: [-55.41, -15.82],
                CENTRALIZAR_IMOVEL: [1246, 500]
            },
            TIPO_IMOVEL: {
                UNIDADE_AUTONOMA: "UNIDADE AUTÔNOMA"
            },
            LAYERS: {
                PONTO_UNIDADE_AUTONOMA: 'pontoUnidadeAutonoma'
            }
        });
}());

(function() {
    angular.module('su-fiscalizacao').directive('graficoLengedaFiscalizacao', ['$timeout', '$mdDialog', 'PainelGerencialService', directive]);

    function directive($timeout, $mdDialog, service) {
        return {
            restrict: 'EA',
            scope: {
                tituloGrafico: '@',
                tipo: '@',
                identificador: '@',
                grafico: '=',
                total: '=',
                uf: '=',
                inicial: '=',
                final: '=',

            },
            templateUrl: 'scripts-fiscalizacao/app/directives/grafico-legenda/grafico.legenda.tmpl.html',
            link: function($scope, $filter) {

                var scope = $scope;
                scope.image = {};

                scope.options = {
                    scales: {
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                callback: function(value) {
                                    if (value % 1 === 0) {
                                        return value;
                                    }
                                }
                            }
                        }]
                    }
                };

                scope.showAlertCaractMotiv = function(ev) {
                    if (scope.identificador === 'caracterizacao') {
                        scope.getCaracterizacaoImoveluf(function() {
                            scope.openModal(ev);
                        });
                    } else {
                        scope.getDemandaMotivacaouf(function() {
                            scope.openModal(ev);
                        });
                    }

                };

                scope.showAlertNaturezaAnual = function(ev) {
                    if (scope.identificador === 'natureza') {
                        scope.getRelatoriosValidosPorNaturezaUf(function() {
                            scope.openModal(ev);
                        });
                    } else {
                        scope.getDemandasExecutadasPorPlanoAnualUf(function() {
                            scope.openModal(ev);
                        });
                    }

                };

                scope.showAlertAssunto = function(ev) {
                    scope.getDemandasExecutadasPorAssuntoUf(function() {
                        scope.openModal(ev);
                    });

                };

                scope.openModal = function(ev) {
                    $mdDialog.show({
                        controller: function() {
                            return scope;
                        },
                        controllerAs: 'scope',
                        templateUrl: 'scripts-fiscalizacao/app/directives/grafico-legenda/modalGraficoUfs.tmpl.html',
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        autoWrap: false,
                        focusOnOpen: true,
                        clickOutsideToClose: true,
                        fullscreen: true,
                        disableParentScroll: true,
                        onComplete: function() {
                            scope.scrollToTop();
                        }
                    });
                };

                /**
                 * Função responsável para dar um scroll automático para o topo da tela ao abrir uma modal
                 */
                scope.scrollToTop = function() {
                    var elmnt = document.getElementById('modalGraficos');
                    elmnt.scrollTop = 0;
                };

                scope.cancelModal = function(callback) {
                    $mdDialog.cancel();

                    if (callback) {
                        callback();
                    }
                };


                scope.getCaracterizacaoImoveluf = function(callback) {

                    scope.filtros = {};
                    scope.filtros.estado = scope.uf;
                    scope.filtros.dtInicio = scope.inicial;
                    scope.filtros.dtFim = scope.final;

                    colors = [
                        '#599BD5',
                        '#EC7D31',
                        '#A5A5A5',
                        '#FEBF00'
                    ];
                    service.getCaracterizacaoImovelPorUf(scope.filtros).then(function(objectReturn) {

                        scope._listaGrafico = [];
                        angular.forEach(objectReturn.data, function(graficoUf, key) {
                            var totalUf = 0;

                            var caracterizacao = scope.criaGrafico(colors);
                            caracterizacao.uf = graficoUf.uf;

                            for (var i = 0; i < graficoUf.graficoDTO.length; i++) {
                                var data = graficoUf.graficoDTO[i];

                                totalUf = totalUf + data.quantidade;
                                caracterizacao.data.push(data.quantidade);
                                caracterizacao.labels.push(data.nome);
                                caracterizacao.series.push(data.nome);
                            }

                            caracterizacao.total = totalUf;

                            scope._listaGrafico.push(caracterizacao);
                        });

                        if (callback) {
                            callback();
                        }

                    });
                };

                scope.getDemandaMotivacaouf = function(callback) {

                    scope.filtros = {};
                    scope.filtros.estado = scope.uf;
                    scope.filtros.dtInicio = scope.inicial;
                    scope.filtros.dtFim = scope.final;

                    colors = [
                        '#599BD5',
                        '#EC7D31',
                        '#A5A5A5',
                        '#FEBF00',
                        '#4872BD',
                        '#72AB4A',
                        '#2A5F87',
                        '#974914',
                        '#626262',
                        '#967207'
                    ];
                    service.getDemandaPorMotivacaoPorUf(scope.filtros).then(function(objectReturn) {
                        scope._listaGrafico = [];
                        angular.forEach(objectReturn.data, function(graficoUf, key) {
                            var totalUf = 0;

                            var caracterizacao = scope.criaGrafico(colors);
                            caracterizacao.uf = graficoUf.uf;

                            for (var i = 0; i < graficoUf.graficoDTO.length; i++) {
                                var data = graficoUf.graficoDTO[i];

                                totalUf = totalUf + data.quantidade;
                                caracterizacao.data.push(data.quantidade);
                                caracterizacao.labels.push(data.nome);
                                caracterizacao.series.push(data.nome);
                            }

                            caracterizacao.total = totalUf;

                            scope._listaGrafico.push(caracterizacao);
                        });

                        if (callback) {
                            callback();
                        }

                    });
                };

                scope.getRelatoriosValidosPorNaturezaUf = function(callback) {

                    scope.filtros = {};
                    scope.filtros.estado = scope.uf;
                    scope.filtros.dtInicio = scope.inicial;
                    scope.filtros.dtFim = scope.final;

                    colors = [
                        '#3A64AE',
                        '#90A4D4'
                    ];
                    service.getRelatoriosValidosPorNaturezaPorUf(scope.filtros).then(function(objectReturn) {
                        scope._listaGrafico = [];
                        angular.forEach(objectReturn.data, function(graficoUf, key) {
                            var totalUf = 0;

                            var caracterizacao = scope.criaGrafico(colors);
                            caracterizacao.uf = graficoUf.uf;

                            for (var i = 0; i < graficoUf.graficoDTO.length; i++) {
                                var data = graficoUf.graficoDTO[i];

                                totalUf = totalUf + data.quantidade;
                                caracterizacao.data.push(data.quantidade);
                                caracterizacao.labels.push(data.nome);
                                caracterizacao.series.push(data.nome);
                            }

                            caracterizacao.total = totalUf;

                            scope._listaGrafico.push(caracterizacao);
                        });

                        if (callback) {
                            callback();
                        }

                    });
                };


                scope.getDemandasExecutadasPorPlanoAnualUf = function(callback) {

                    scope.filtros = {};
                    scope.filtros.estado = scope.uf;
                    scope.filtros.dtInicio = scope.inicial;
                    scope.filtros.dtFim = scope.final;

                    colors = [
                        '#599BD5',
                        '#EC7D31'
                    ];
                    service.getDemandasExecutadasPorPlanoAnualUf(scope.filtros).then(function(objectReturn) {
                        scope._listaGrafico = [];
                        angular.forEach(objectReturn.data, function(graficoUf, key) {
                            var totalUf = 0;

                            var caracterizacao = scope.criaGrafico(colors);
                            caracterizacao.uf = graficoUf.uf;

                            for (var i = 0; i < graficoUf.graficoDTO.length; i++) {
                                var data = graficoUf.graficoDTO[i];

                                totalUf = totalUf + data.quantidade;
                                caracterizacao.data.push(data.quantidade);
                                caracterizacao.labels.push(data.nome);
                                caracterizacao.series.push(data.nome);
                            }

                            caracterizacao.total = totalUf;

                            scope._listaGrafico.push(caracterizacao);
                        });

                        if (callback) {
                            callback();
                        }

                    });
                };

                scope.getDemandasExecutadasPorAssuntoUf = function(callback) {

                    scope.filtros = {};
                    scope.filtros.estado = scope.uf;
                    scope.filtros.dtInicio = scope.inicial;
                    scope.filtros.dtFim = scope.final;

                    colors = [
                        '#599BD5',
                        '#EC7D31',
                        '#A5A5A5',
                        '#FEBF00',
                        '#4872BD',
                        '#72AB4A',
                        '#2A5F87',
                        '#974914',
                        '#626262',
                        '#967207',
                        '#2500ec',
                        '#a5a43a',
                        '#fe3900',
                        '#54bd00',
                        '#ab2e87',
                        '#4a2187',
                        '#946a97',
                        '#00a1a8',
                        '#000000'
                    ];
                    service.getDemandasExecutadasPorAssuntoUf(scope.filtros).then(function(objectReturn) {
                        scope._listaGrafico = [];
                        angular.forEach(objectReturn.data, function(graficoUf, key) {
                            var totalUf = 0;

                            var caracterizacao = scope.criaGraficoBars(colors);
                            caracterizacao.uf = graficoUf.uf;

                            for (var i = 0; i < graficoUf.graficoDTO.length; i++) {
                                var data = graficoUf.graficoDTO[i];

                                totalUf = totalUf + data.quantidade;
                                caracterizacao.data.push(data.quantidade);
                                caracterizacao.labels.push(data.nome);
                                caracterizacao.series.push(data.nome);
                            }

                            caracterizacao.total = totalUf;

                            scope._listaGrafico.push(caracterizacao);
                        });

                        if (callback) {
                            callback();
                        }

                    });
                };

                scope.criaGraficoBars = function(colors) {
                    return {
                        data: [],
                        labels: [],
                        series: [],
                        colors: colors,
                        uf: {},
                        total: {},
                        options: {
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                                labels: {
                                    render: "value",
                                    fontSize: 12,
                                    fontColor: "#000000",
                                    fontStyle: "bold",
                                    showZero: true,
                                }
                            }
                        }
                    };
                };

                scope.criaGrafico = function(colors) {
                    return {
                        data: [],
                        labels: [],
                        series: [],
                        colors: colors,
                        uf: {},
                        total: {},
                        options: {
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                                labels: {
                                    render: "percentage",
                                    precision: 0,
                                    showActualPercentages: true,
                                    fontSize: 12,
                                    fontColor: "#000000",
                                    fontStyle: "bold",
                                }
                            }
                        }
                    };
                };

                scope.cloneCanvas = function() {
                    var oldCanvas = document.getElementById('grafico' + scope.identificador);

                    //create a new canvas
                    var newCanvas = document.createElement('canvas');
                    var context = newCanvas.getContext('2d');

                    //set dimensions
                    newCanvas.width = oldCanvas.width;
                    newCanvas.height = oldCanvas.height;

                    //apply the old canvas to the new one
                    context.drawImage(oldCanvas, 0, 0);

                    //return the new canvas
                    return newCanvas;
                }

                scope.pngGenerator = function() {
                    var elementoGrafico = document.getElementById('pgn-frame');
                    elementoGrafico.innerHTML = '';
                    var elementoLegenda = angular.copy(document.getElementById('legenda' + scope.identificador));

                    var div = document.createElement('div');
                    div.style.paddingTop = '30px';

                    var yearInicial = scope.inicial.getFullYear();
                    var monthInicial = scope.inicial.getMonth() + 1;
                    monthInicial = monthInicial < 10 ? '0' + monthInicial : monthInicial;
                    var dayInicial = scope.inicial.getDate();
                    dayInicial = dayInicial < 10 ? '0' + dayInicial : dayInicial;

                    var dataInicial = dayInicial + '/' + monthInicial + '/' + yearInicial;

                    var yearFinal = scope.final.getFullYear();
                    var monthFinal = scope.final.getMonth() + 1;
                    monthFinal = monthFinal < 10 ? '0' + monthFinal : monthFinal;
                    var dayFinal = scope.final.getDate();
                    dayFinal = dayFinal < 10 ? '0' + dayFinal : dayFinal;

                    var dataFinal = dayFinal + '/' + monthFinal + '/' + yearFinal;

                    div.innerHTML = '<div style="margin: 10px 0"><strong><h3>' + scope.tituloGrafico.toUpperCase() + '</h3></strong></div>';
                    div.innerHTML += '<div style="margin: 10px 0" ><strong>UF(s): </strong>' + scope.uf.toString() + '</div>';
                    div.innerHTML += '<div style="margin: 10px 0" ><strong>Período: </strong>' + dataInicial + ' a ' + dataFinal + '</div>';
                    div.innerHTML += '<div style="margin: 10px 0" ><strong>Total: </strong>' + scope.total.toString() + '</div>';

                    var canvas = scope.cloneCanvas();
                    div.appendChild(canvas);
                    if (elementoLegenda)
                        div.appendChild(elementoLegenda);

                    elementoGrafico.appendChild(div);
                }

                scope.gerarPng = function() {
                    scope.pngGenerator();
                    var elementoGrafico = document.getElementById('pgn-frame');

                    domtoimage.toPng(elementoGrafico).then(function(dataUrl) {
                        var link = document.createElement('a');
                        scope.pngChart = angular.copy(dataUrl);
                        link.download = scope.tituloGrafico.toUpperCase();
                        link.href = dataUrl;
                        link.click();
                        elementoGrafico.innerHTML = '';
                    });
                };

                scope.pdfJsonGenerator = function() {

                    var today = new Date();
                    var year = today.getFullYear();
                    var month = today.getMonth() + 1;
                    if (month < 10) {
                        month = '0' + month;
                    }
                    var day = today.getDate();
                    if (day < 10) {
                        day = '0' + day;
                    }
                    var fullYear = day + '/' + month + '/' + year;



                    var yearInicial = scope.inicial.getFullYear();
                    var monthInicial = scope.inicial.getMonth() + 1;
                    monthInicial = monthInicial < 10 ? '0' + monthInicial : monthInicial;
                    var dayInicial = scope.inicial.getDate();
                    dayInicial = dayInicial < 10 ? '0' + dayInicial : dayInicial;

                    var dataInicial = dayInicial + '/' + monthInicial + '/' + yearInicial;

                    var yearFinal = scope.final.getFullYear();
                    var monthFinal = scope.final.getMonth() + 1;
                    monthFinal = monthFinal < 10 ? '0' + monthFinal : monthFinal;
                    var dayFinal = scope.final.getDate();
                    dayFinal = dayFinal < 10 ? '0' + dayFinal : dayFinal;

                    var dataFinal = dayFinal + '/' + monthFinal + '/' + yearFinal;

                    var defContent = [{
                            style: 'table',

                            table: {
                                widths: ['*'],
                                body: [
                                    [
                                        { text: fullYear, bold: false, fontSize: 10, alignment: 'right' }
                                    ]
                                ]
                            },

                            layout: 'noBorders'
                        },
                        {
                            style: 'table',

                            table: {
                                widths: ['*'],
                                body: [
                                    [
                                        { text: scope.tituloGrafico, bold: true, fontSize: 10, alignment: 'center', margin: [0, 5, 0, 5] }
                                    ]
                                ]
                            },

                            layout: 'noBorders'
                        },
                        {
                            style: 'table',
                            table: {
                                widths: ['*'],
                                body: [
                                    [
                                        { text: 'UF(s): ' + scope.uf.toString(), bold: false, alignment: 'left', fontSize: 10, margin: [0, 5, 0, 5] }
                                    ]
                                ]
                            },
                            layout: 'noBorders'
                        },
                        {
                            style: 'table',
                            table: {
                                widths: ['*'],
                                body: [
                                    [
                                        { text: 'Período: ' + dataInicial + ' a ' + dataFinal, bold: false, alignment: 'left', fontSize: 10, margin: [0, 5, 0, 5] }
                                    ]
                                ]
                            },
                            layout: 'noBorders'
                        },
                        {
                            style: 'table',
                            table: {
                                widths: ['*'],
                                body: [
                                    [
                                        { text: 'Total: ' + scope.total.toString(), bold: false, alignment: 'left', fontSize: 10, margin: [0, 5, 0, 5] }
                                    ]
                                ]
                            },
                            layout: 'noBorders'
                        },
                        {
                            style: 'table',

                            table: {
                                widths: ['*'],
                                alignment: ['center'],
                                body: [
                                    [
                                        { image: scope.pngChartGrafico, alignment: 'center', width: 300, margin: [0, 0, 0, 10] }
                                    ]
                                ]
                            },

                            layout: 'noBorders'
                        },
                        {
                            style: 'table',

                            table: {
                                widths: ['*'],
                                alignment: ['center'],
                                body: [
                                    [{
                                        image: scope.pngChartLegenda,
                                        alignment: 'center',
                                        width: (scope.identificador === 'natureza' || scope.identificador === 'Anual') ? 300 : 500,
                                        margin: [0, 0, 0, 10]
                                    }]
                                ]
                            },

                            layout: 'noBorders'
                        }
                    ];

                    var defContentSemLegenda = [{
                            style: 'table',

                            table: {
                                widths: ['*'],
                                body: [
                                    [
                                        { text: fullYear, bold: false, fontSize: 10, alignment: 'right' }
                                    ]
                                ]
                            },

                            layout: 'noBorders'
                        },
                        {
                            style: 'table',

                            table: {
                                widths: ['*'],
                                body: [
                                    [
                                        { text: scope.tituloGrafico, bold: true, fontSize: 10, alignment: 'center', margin: [0, 5, 0, 5] }
                                    ]
                                ]
                            },

                            layout: 'noBorders'
                        },
                        {
                            style: 'table',
                            table: {
                                widths: ['*'],
                                body: [
                                    [
                                        { text: 'UF(s): ' + scope.uf.toString(), bold: false, alignment: 'left', fontSize: 10, margin: [0, 5, 0, 5] }
                                    ]
                                ]
                            },
                            layout: 'noBorders'
                        },
                        {
                            style: 'table',
                            table: {
                                widths: ['*'],
                                body: [
                                    [
                                        { text: 'Período: ' + scope.inicial.toString() + ' a ' + scope.final.toString(), bold: false, alignment: 'left', fontSize: 10, margin: [0, 5, 0, 5] }
                                    ]
                                ]
                            },
                            layout: 'noBorders'
                        },
                        {
                            style: 'table',
                            table: {
                                widths: ['*'],
                                body: [
                                    [
                                        { text: 'Total: ' + scope.total.toString(), bold: false, alignment: 'left', fontSize: 10, margin: [0, 5, 0, 5] }
                                    ]
                                ]
                            },
                            layout: 'noBorders'
                        },
                        {
                            style: 'table',

                            table: {
                                widths: ['*'],
                                alignment: ['center'],
                                body: [
                                    [
                                        { image: scope.pngChartGrafico, alignment: 'center', width: 500, margin: [0, 0, 0, 10] }
                                    ]
                                ]
                            },

                            layout: 'noBorders'
                        }
                    ];

                    return {
                        content: scope.identificador === "assunto" ? defContentSemLegenda : defContent,
                        pageSize: 'A4',
                        pageOrientation: 'portrait',
                        // [left, top, right, bottom] or [horizontal, vertical] or just a number for equal margins
                        pageMargins: [20, 20]
                    };
                };

                scope.downloadPDF = function() {
                    var elementoGrafico = document.getElementById('grafico' + scope.identificador);
                    var elementoLegenda = document.getElementById('legenda' + scope.identificador);

                    // pega imagem grafico 
                    domtoimage.toPng(elementoGrafico).then(function(dataUrl) {
                        scope.pngChartGrafico = angular.copy(dataUrl);
                        if (elementoLegenda) {
                            domtoimage.toPng(elementoLegenda).then(function(dataUrlLegenda) {
                                scope.pngChartLegenda = angular.copy(dataUrlLegenda);

                                var pdf = scope.pdfJsonGenerator();

                                pdfMake.createPdf(pdf).download(scope.tituloGrafico.toUpperCase() + ".pdf");
                            });
                        } else {
                            var pdf = scope.pdfJsonGenerator();

                            pdfMake.createPdf(pdf).download(scope.tituloGrafico.toUpperCase() + ".pdf");
                        }

                    });
                };
            }
        };
    }

})();
(function () {
    'use strict';
    angular.module('su-fiscalizacao')
        .factory('RelatorioInfracaoService', ["$rootScope", "$mdDialog", "$q", function ($rootScope, $mdDialog, $q) {

            var TIPO_IMOVEL_DOMINIAL = 'DOMINIAL';
            var TIPO_IMOVEL_BEM_USO_COMUM = 'BEM_USO_COMUM';
            var TIPO_IMOVEL_BEM_USO_ESPECIAL = 'BENS_USO_ESPECIAL';

            /**
             * Cria os objetos e atributos necessários para uma ocorrência de infração
             * @param autuado
             * @returns {*}
             */
            function initInfracao (autuado, callback) {
                autuado.ocorrenciaInfracao = {};

                //Limpa os campos da área autuada da ocorrência de infração
                autuado.ocorrenciaInfracao.areaConstrucao = null;
                autuado.ocorrenciaInfracao.areaConstruida = null;
                autuado.ocorrenciaInfracao.areaCercamento = null;
                autuado.ocorrenciaInfracao.areaAterro = null;
                autuado.ocorrenciaInfracao.areaEquipamento = null;
                autuado.ocorrenciaInfracao.areaDesmatamento = null;
                autuado.ocorrenciaInfracao.areaOutrasBenfeitorias = null;
                autuado.ocorrenciaInfracao.areaObra = null;

                //limpa as perguntas do questionário da ocorrência de infração
                autuado.ocorrenciaInfracao.perguntaUm = null;
                autuado.ocorrenciaInfracao.perguntaDois = null;
                autuado.ocorrenciaInfracao.perguntaTres = null;
                autuado.ocorrenciaInfracao.perguntaQuatro = null;
                autuado.ocorrenciaInfracao.perguntaCinco = null;
                autuado.ocorrenciaInfracao.perguntaSeis = null;
                autuado.ocorrenciaInfracao.perguntaSete = null;
                autuado.ocorrenciaInfracao.perguntaOito = null;
                autuado.ocorrenciaInfracao.perguntaNove = null;
                autuado.ocorrenciaInfracao.perguntaDez = null;
                autuado.ocorrenciaInfracao.perguntaOnze = null;
                autuado.ocorrenciaInfracao.perguntaDoze = null;
                autuado.ocorrenciaInfracao.perguntaTreze = null;
                autuado.ocorrenciaInfracao.perguntaQuatorze = null;
                autuado.ocorrenciaInfracao.perguntaQuinze = null;

                if (callback) {
                    callback();
                }
            }

            /**
             * Verifica se uma ocorrência de infração já foi preenchida, caso sim recupera os dados da occorência de infração
             * @param ocorrenciaInfracao
             * @param callback
             */
            function initOcorrenciaInfracaoExistente (ocorrenciaInfracao) {
                !$rootScope.ocorrenciaInfracao ? $rootScope.ocorrenciaInfracao = {} : "";

                //popula as perguntas da ocorrencia de infração
                if (ocorrenciaInfracao.perguntaUm || !ocorrenciaInfracao.perguntaUm) {
                    $rootScope.ocorrenciaInfracao.perguntaUmCopy = angular.copy(ocorrenciaInfracao.perguntaUm);
                }
                if (ocorrenciaInfracao.perguntaDois || !ocorrenciaInfracao.perguntaDois) {
                    $rootScope.ocorrenciaInfracao.perguntaDoisCopy = angular.copy(ocorrenciaInfracao.perguntaDois);
                }
                if (ocorrenciaInfracao.perguntaTres || !ocorrenciaInfracao.perguntaTres) {
                    $rootScope.ocorrenciaInfracao.perguntaTresCopy = angular.copy(ocorrenciaInfracao.perguntaTres);
                }
                if (ocorrenciaInfracao.perguntaQuatro || !ocorrenciaInfracao.perguntaQuatro) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatroCopy = angular.copy(ocorrenciaInfracao.perguntaQuatro);
                }
                if (ocorrenciaInfracao.perguntaCinco || !ocorrenciaInfracao.perguntaCinco) {
                    $rootScope.ocorrenciaInfracao.perguntaCincoCopy = angular.copy(ocorrenciaInfracao.perguntaCinco);
                }
                if (ocorrenciaInfracao.perguntaSeis || !ocorrenciaInfracao.perguntaSeis) {
                    $rootScope.ocorrenciaInfracao.perguntaSeisCopy = angular.copy(ocorrenciaInfracao.perguntaSeis);
                }
                if (ocorrenciaInfracao.perguntaSete || !ocorrenciaInfracao.perguntaSete) {
                    $rootScope.ocorrenciaInfracao.perguntaSeteCopy = angular.copy(ocorrenciaInfracao.perguntaSete);
                }
                if (ocorrenciaInfracao.perguntaOito || !ocorrenciaInfracao.perguntaOito) {
                    $rootScope.ocorrenciaInfracao.perguntaOitoCopy = angular.copy(ocorrenciaInfracao.perguntaOito);
                }
                if (ocorrenciaInfracao.perguntaNove || !ocorrenciaInfracao.perguntaNove) {
                    $rootScope.ocorrenciaInfracao.perguntaNoveCopy = angular.copy(ocorrenciaInfracao.perguntaNove);
                }
                if (ocorrenciaInfracao.perguntaDez || !ocorrenciaInfracao.perguntaDez) {
                    $rootScope.ocorrenciaInfracao.perguntaDezCopy = angular.copy(ocorrenciaInfracao.perguntaDez);
                }
                if (ocorrenciaInfracao.perguntaOnze || !ocorrenciaInfracao.perguntaOnze) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = angular.copy(ocorrenciaInfracao.perguntaOnze);
                }
                if (ocorrenciaInfracao.perguntaDoze || !ocorrenciaInfracao.perguntaDoze) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = angular.copy(ocorrenciaInfracao.perguntaDoze);
                }
                if (ocorrenciaInfracao.perguntaTreze || !ocorrenciaInfracao.perguntaTreze) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = angular.copy(ocorrenciaInfracao.perguntaTreze);
                }
                if (ocorrenciaInfracao.perguntaQuatorze || !ocorrenciaInfracao.perguntaQuatorze) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = angular.copy(ocorrenciaInfracao.perguntaQuatorze);
                }
                if (ocorrenciaInfracao.perguntaQuinze || !ocorrenciaInfracao.perguntaQuinze) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = angular.copy(ocorrenciaInfracao.perguntaQuinze);
                }

                //popula as  áreas de autuação da ocorrencia de infração
                if (ocorrenciaInfracao.areaEquipamento) {
                    $rootScope.ocorrenciaInfracao.areaEquipamento = angular.copy(ocorrenciaInfracao.areaEquipamento);
                }
                if (ocorrenciaInfracao.areaOutrasBenfeitorias) {
                    $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = angular.copy(ocorrenciaInfracao.areaOutrasBenfeitorias);
                }
                if (ocorrenciaInfracao.areaDesmatamento) {
                    $rootScope.ocorrenciaInfracao.areaDesmatamento = angular.copy(ocorrenciaInfracao.areaDesmatamento);
                }
                if (ocorrenciaInfracao.areaCercamento) {
                    $rootScope.ocorrenciaInfracao.areaCercamento = angular.copy(ocorrenciaInfracao.areaCercamento);
                }
                if (ocorrenciaInfracao.areaObra) {
                    $rootScope.ocorrenciaInfracao.areaObra = angular.copy(ocorrenciaInfracao.areaObra);
                }
                if (ocorrenciaInfracao.areaConstrucao) {
                    $rootScope.ocorrenciaInfracao.areaConstrucao = angular.copy(ocorrenciaInfracao.areaConstrucao);
                }
                if (ocorrenciaInfracao.areaAterro) {
                    $rootScope.ocorrenciaInfracao.areaAterro = angular.copy(ocorrenciaInfracao.areaAterro);
                }

                return $q(function(resolve, reject){
                    if($rootScope.ocorrenciaInfracao){
                        resolve($rootScope.ocorrenciaInfracao);
                    }else{
                        reject();
                    }
                });
            }

            /**
             * Limpa a pergunta 1 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaUm (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDoisCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDoisCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTresCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTresCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatroCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatroCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaCincoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaCincoCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeisCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeisCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeteCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeteCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOitoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOitoCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaNoveCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaNoveCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDezCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDezCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOnzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 2 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaDois (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTresCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTresCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatroCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatroCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaCincoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaCincoCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeisCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeisCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeteCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeteCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOitoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOitoCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaNoveCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaNoveCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDezCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDezCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOnzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 3 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaTres (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatroCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatroCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaCincoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaCincoCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeisCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeisCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeteCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeteCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOitoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOitoCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaNoveCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaNoveCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDezCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDezCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOnzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 4 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaQuatro (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaCincoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaCincoCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeisCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeisCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeteCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeteCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOitoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOitoCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaNoveCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaNoveCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDezCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDezCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOnzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 5 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaCinco (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeisCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeisCopy = null;

                    //quando destinação for bem de uso dominial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_DOMINIAL) {
                        $rootScope.ocorrenciaInfracao.areaEquipamento ? $rootScope.ocorrenciaInfracao.areaEquipamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeteCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeteCopy = null;

                    //quando destinação for bem de uso dominial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_DOMINIAL) {
                        $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias ? $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOitoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOitoCopy = null;

                    //quando destinação for bem de uso dominial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_DOMINIAL) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaNoveCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaNoveCopy = null;

                    //quando destinação for bem de uso dominial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_DOMINIAL) {
                        $rootScope.ocorrenciaInfracao.areaCercamento ? $rootScope.ocorrenciaInfracao.areaCercamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDezCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDezCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOnzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 6 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaSeis (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaSeteCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaSeteCopy = null;

                    //quando destinação for bem de uso dominial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_DOMINIAL) {
                        $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias ? $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOitoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOitoCopy = null;

                    //quando destinação for bem de uso dominial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_DOMINIAL) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaNoveCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaNoveCopy = null;

                    //quando destinação for bem de uso dominial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_DOMINIAL) {
                        $rootScope.ocorrenciaInfracao.areaCercamento ? $rootScope.ocorrenciaInfracao.areaCercamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDezCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDezCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOnzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 7 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaSete (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOitoCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOitoCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.arecaCercamento ? $rootScope.ocorrenciaInfracao.arecaCercamento = null : '';
                    }

                    //quando destinação for bem de uso especial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_ESPECIAL) {
                        $rootScope.ocorrenciaInfracao.areaEquipamento ? $rootScope.ocorrenciaInfracao.areaEquipamento = null : '';
                    }

                    //quando destinação for bem de uso dominial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_DOMINIAL) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaNoveCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaNoveCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaAterro ? $rootScope.ocorrenciaInfracao.areaAterro = null : '';
                    }

                    //quando destinação for bem de uso especial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_ESPECIAL) {
                        $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias ? $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = null : '';
                    }

                    //quando destinação for bem de uso dominial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_DOMINIAL) {
                        $rootScope.ocorrenciaInfracao.areaCercamento ? $rootScope.ocorrenciaInfracao.areaCercamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDezCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDezCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }

                    //quando destinação for bem de uso especial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_ESPECIAL) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOnzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }

                    //quando destinação for bem de uso especial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_ESPECIAL) {
                        $rootScope.ocorrenciaInfracao.areaCercamento ? $rootScope.ocorrenciaInfracao.areaCercamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias ? $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 8 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaOito (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaNoveCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaNoveCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaAterro ? $rootScope.ocorrenciaInfracao.areaAterro = null : '';
                    }

                    //quando destinação for bem de uso especial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_ESPECIAL) {
                        $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias ? $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = null : '';
                    }

                    //quando destinação for bem de uso dominial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_DOMINIAL) {
                        $rootScope.ocorrenciaInfracao.areaCercamento ? $rootScope.ocorrenciaInfracao.areaCercamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDezCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDezCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaEquipamento ? $rootScope.ocorrenciaInfracao.areaEquipamento = null : '';
                    }

                    //quando destinação for bem de uso especial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_ESPECIAL) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOnzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }

                    //quando destinação for bem de uso especial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_ESPECIAL) {
                        $rootScope.ocorrenciaInfracao.areaCercamento ? $rootScope.ocorrenciaInfracao.areaCercamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias ? $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 9 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaNove (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDezCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDezCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaAterro ? $rootScope.ocorrenciaInfracao.areaAterro = null : '';
                    }

                    //quando destinação for bem de uso especial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_ESPECIAL) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOnzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaEquipamento ? $rootScope.ocorrenciaInfracao.areaEquipamento = null : '';
                    }

                    //quando destinação for bem de uso especial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_ESPECIAL) {
                        $rootScope.ocorrenciaInfracao.areaCercamento ? $rootScope.ocorrenciaInfracao.areaCercamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias ? $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 10 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaDez (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaOnzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaOnzeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaEquipamento ? $rootScope.ocorrenciaInfracao.areaEquipamento = null : '';
                    }

                    //quando destinação for bem de uso especial
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_ESPECIAL) {
                        $rootScope.ocorrenciaInfracao.areaCercamento ? $rootScope.ocorrenciaInfracao.areaCercamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias ? $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 11 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaOnze (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaDozeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaDozeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaDesmatamento ? $rootScope.ocorrenciaInfracao.areaDesmatamento = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias ? $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 12 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaDoze (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaTrezeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaTrezeCopy = null;

                    //quando destinação for bem de uso comum
                    if (relatorioFiscalizacao.tipoImovel.tipo === TIPO_IMOVEL_BEM_USO_COMUM) {
                        $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias ? $rootScope.ocorrenciaInfracao.areaOutrasBenfeitorias = null : '';
                    }
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 13 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaTreze (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuatorzeCopy = null;
                }
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Limpa a pergunta 14 do questionário de ocorrência de infração
             * @param callback
             */
            function cleanPerguntaQuatorze (relatorioFiscalizacao, callback) {
                if ($rootScope.ocorrenciaInfracao && $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy) {
                    $rootScope.ocorrenciaInfracao.perguntaQuinzeCopy = null;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Verifica se as áreas(
             * areaConstrução, areaCercamento, areaAterro, areaEquipamento, areaDesmatamento, outrasBenfeitorias, areaObra
             * ) foram preenchidas e calcula a área autuada.
             * @param callback
             */
            function calculaAreaAutuada (ocorrenciaInfracao, callback) {
                var areaConstrucao = ocorrenciaInfracao && ocorrenciaInfracao.areaConstrucao ? parseFloat(ocorrenciaInfracao.areaConstrucao) : 0;
                var areaConstruida = ocorrenciaInfracao && ocorrenciaInfracao.areaConstruida ? parseFloat(ocorrenciaInfracao.areaConstruida) : 0;
                var areaCercamento = ocorrenciaInfracao && ocorrenciaInfracao.areaCercamento ? parseFloat(ocorrenciaInfracao.areaCercamento) : 0;
                var areaAterro = ocorrenciaInfracao && ocorrenciaInfracao.areaAterro ? parseFloat(ocorrenciaInfracao.areaAterro) : 0;
                var areaEquipamento = ocorrenciaInfracao && ocorrenciaInfracao.areaEquipamento ? parseFloat(ocorrenciaInfracao.areaEquipamento) : 0;
                var areaDesmatamento = ocorrenciaInfracao && ocorrenciaInfracao.areaDesmatamento ? parseFloat(ocorrenciaInfracao.areaDesmatamento) : 0;
                var outrasBenfeitorias = ocorrenciaInfracao && ocorrenciaInfracao.areaOutrasBenfeitorias ? parseFloat(ocorrenciaInfracao.areaOutrasBenfeitorias) : 0;
                var areaObra = ocorrenciaInfracao && ocorrenciaInfracao.areaObra ? parseFloat(ocorrenciaInfracao.areaObra) : 0;

                if (ocorrenciaInfracao) {
                    ocorrenciaInfracao.areaAutuada = areaConstrucao + areaConstruida + areaCercamento + areaAterro + areaEquipamento + areaDesmatamento + outrasBenfeitorias + areaObra;
                    ocorrenciaInfracao.areaAutuada > 0 ? ocorrenciaInfracao.showAreaAutuada = true : ocorrenciaInfracao.showAreaAutuada = false;
                }

                if (callback) {
                    callback();
                }
            }

            /**
             * Abre a modal de cadastro de documento
             * @param ev
             * @param documento
             * @param relatorio
             * @param minDataFiscalizacao
             */
            function openModal(event, documento, relatorioFiscalizacao, autuado, minDataFiscalizacao, scope) {
                $mdDialog.show({
                    controller: 'RelatorioDocumentoController as scope',
                    templateUrl: 'scripts-fiscalizacao/pages/relatorio/documento/view/relatorio-documento.html',
                    parent: angular.element(document.body),
                    targetEvent: event,
                    clickOutsideToClose: true,
                    fullscreen: true,
                    autoWrap: false,
                    disableParentScroll: true,
                    focusOnOpen: true,
                    locals: {
                        documento: documento,
                        relatorioFiscalizacao: relatorioFiscalizacao,
                        autuado:autuado,
                        minDataFiscalizacao: minDataFiscalizacao
                    },
                    onComplete: function () {
                        scope.documento = angular.copy(documento);

                        if (documento.tipoDocumento) {
                            scope.documento.tipoDocumento = angular.copy(documento.tipoDocumento);
                        }

                        if (documento.dataAutuacao) {
                            scope.documento.dataAutuacao = angular.copy(new Date(documento.dataAutuacao));
                        } else {
                            scope.documento.dataAutuacao = '';
                        }

                        if (documento.dataNascimento) {
                            scope.documento.dataNascimento = angular.copy(new Date(documento.dataNascimento));
                        } else {
                            scope.documento.dataNascimento = '';
                        }

                        scope.documento.apenasLeitura = angular.copy(documento.apenasLeitura);
                    }
                });
            }

            /**
             * Abre a modal de justificativa de exclusão/edição do auto de infração
             * @param event
             * @param documento
             * @param relatorioFiscalizacao
             * @param autuado
             * @param minDataFiscalizacao
             * @param tipoJustificativa
             * @param scope
             */
            function openModalJustificativa(event, documento, relatorioFiscalizacao, autuado, minDataFiscalizacao, tipoJustificativa, scope) {
                $mdDialog.show({
                    controller: 'JustificativaDocumentoController as scope',
                    templateUrl: 'scripts-fiscalizacao/pages/relatorio/justificativa-documento/view/justificativa-documento.html',
                    parent: angular.element(document.body),
                    targetEvent: event,
                    clickOutsideToClose: true,
                    fullscreen: true,
                    autoWrap: false,
                    disableParentScroll: true,
                    focusOnOpen: true,
                    locals: {
                        documento: documento,
                        relatorioFiscalizacao: relatorioFiscalizacao,
                        autuado:autuado,
                        tipoJustificativa: tipoJustificativa,
                        minDataFiscalizacao: minDataFiscalizacao
                    },
                    onComplete: function () {
                        scope.documento = documento;
                    }
                });
            }

            /**
             * Verifica se existem documentos para o autuado e quais os tipos de infração o autuado foi submetido
             * @param callback
             */
            function verificaDocumentos (autuado, callback) {
                autuado.existeNotificacao = false;
                autuado.existeEmbargo = false;
                autuado.existeInfracao = false;


                if (autuado.documentos.length > 0) {
                    for (var i = 0; i < autuado.documentos.length; i++) {
                        if (autuado.documentos[i].tipoDocumento === 'AUTO_DE_INFRACAO') {
                            autuado.existeInfracao = true;
                        } else if (autuado.documentos[i].tipoDocumento === 'NOTIFICACAO') {
                            autuado.existeNotificacao = true;
                        } else if (autuado.documentos[i].tipoDocumento === 'AUTO_DE_EMBARGO') {
                            autuado.existeEmbargo = true;
                        }
                    }
                }

                if (callback) {
                    callback();
                }
            }

            return {
                initInfracao: initInfracao,
                initOcorrenciaInfracaoExistente: initOcorrenciaInfracaoExistente,
                cleanPerguntaUm: cleanPerguntaUm,
                cleanPerguntaDois: cleanPerguntaDois,
                cleanPerguntaTres: cleanPerguntaTres,
                cleanPerguntaQuatro: cleanPerguntaQuatro,
                cleanPerguntaCinco: cleanPerguntaCinco,
                cleanPerguntaSeis: cleanPerguntaSeis,
                cleanPerguntaSete: cleanPerguntaSete,
                cleanPerguntaOito: cleanPerguntaOito,
                cleanPerguntaNove: cleanPerguntaNove,
                cleanPerguntaDez: cleanPerguntaDez,
                cleanPerguntaOnze: cleanPerguntaOnze,
                cleanPerguntaDoze: cleanPerguntaDoze,
                cleanPerguntaTreze: cleanPerguntaTreze,
                cleanPerguntaQuatorze: cleanPerguntaQuatorze,
                calculaAreaAutuada: calculaAreaAutuada,
                openModal: openModal,
                openModalJustificativa: openModalJustificativa,
                verificaDocumentos: verificaDocumentos,
            };
        }])

})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao')
        .factory("MapaServiceFiscalizacao", ["$http", "$q", function ($http, $q) {
            var demandas = [];

            var gerarOrdemFiscalizacao = function (registros) {
                demandas = angular.copy(registros);
            };

            var getOrdemFiscalizacao = function (demandas) {

                return $q(function(resolve, reject){
                    if(demandas){
                        resolve(angular.copy(demandas));
                    }else{
                        reject();
                    }
                });
            };

            var limpaDemandas = function () {
                demandas = null;
            };

            return {
                gerarOrdemFiscalizacao:gerarOrdemFiscalizacao,
                getOrdemFiscalizacao:getOrdemFiscalizacao,
                limpaDemandas:limpaDemandas
            }
        }])
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao')
        .factory("IntegradorService", ["$http", function ($http) {
            var baseUrl = '/fiscalizacao/api/integracoes';

            var getEstados = function () {
                return $http.get(baseUrl + "/estados");
            };

            var getMunicipios = function (sigla) {
                return $http.get(baseUrl + "/municipios/" + sigla);
            };

            var getCEP = function (cep) {
                return $http.get(baseUrl + "/cep/"+cep);
            };
            var getRIP = function (rip) {
                return $http.get(baseUrl + "/rip/"+rip);
            };
            var validar = function (registro) {
                return $http.post(baseUrl + "/validar",registro);
            };
            return {
                getEstados: getEstados,
                getMunicipios: getMunicipios,
                getCEP:getCEP,
                getRIP:getRIP,
                validar:validar
            }
        }])
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao')
        .factory("GeoService", ["$resource", function ($resource) {

			var baseUrl = '/geoservicos/api/';

            return {
                estados: $resource(baseUrl + 'unidade-federacao'),
                municipios: $resource(baseUrl + 'municipio/simples')
            }
        }])
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao')
        .factory('BuscarCpfServiceFiscalizacao', ["$http", function ($http) {
            var baseUrlCpf = '/fiscalizacao/api/dados-pessoa/buscarcpf/';
            var baseUrlCnpj = '/fiscalizacao/api/dados-pessoa/buscarcnpj/';

            var buscarPessoa = function (cpf) {
                return $http.get(baseUrlCpf + cpf);
            };

            var buscarCnpj = function (cnpj) {
                return $http.get(baseUrlCnpj + cnpj);
            };

            return {
                buscarPessoa: buscarPessoa,
                buscarCnpj: buscarCnpj
            };
        }]);
})();




(function () {
    'use strict';
    angular.module('su-fiscalizacao')
    .factory('ArquivoServiceFiscalizacao', ["$http", "Upload", function ($http, Upload) {

        var URL_ARQUIVO = '/fiscalizacao/api/arquivo';

        var uploadArquivo = function ($file) {
            return Upload.upload({
                url: URL_ARQUIVO + '/upload',
                data: {
                    file: $file
                }
            });
        };

        return {
            uploadArquivo: uploadArquivo
        };

    }]);
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').factory('successInterceptorFiscalizacao', ["$q", "$injector", function ($q, $injector) {

        var toastr;

        function getToaster() {
            if (!toastr) {
                toastr = $injector.get('ComumFactory');
            }
            return toastr;
        }

        return {
            'response': function (response) {
                if (response.headers('su-fiscalizacao-success')) {
                    getToaster().mostrarMensagemSucesso(response.headers('su-fiscalizacao-success'));
                }
                return response;
            }
        };
    }]);
})();


(function () {
    'use strict';
    angular.module('su-fiscalizacao')
    .factory('errorInterceptorFiscalizacao', ["$q", "$rootScope", "$window", "$injector", function ($q, $rootScope, $window, $injector) {

        var toastr;

        function getToaster() {
            if (!toastr) {
                toastr = $injector.get('ComumFactory');
            }
            return toastr;
        }

        return {
            'responseError': function (response) {
                if(!(response.status === 401 && response.data.path.indexOf("/api/account") === 0 )){
                    $rootScope.$emit('su-fiscalizacao.httpError', response);
                }

                if(response.status === 403){
               //     $window.location.href = '/fiscalizacao/api';
                }

                if (response.status === 400 && response.headers('su-fiscalizacao-error')) {
                    getToaster().mostrarMensagemError(response.headers('su-fiscalizacao-error'));
                }

                return $q.reject(response);
            }
        };
    }]);
})();


(function(){
    'use strict';
    angular.module('su-fiscalizacao').filter('stringLimit', function(){
        return function(str){
            if(str.length >= 400){
                return str.substring(0, 400);
            }
            return str;
        };
    });
})();

(function(){
    'use strict';
    angular.module('su-fiscalizacao').filter('stringLimitMap', function(){
        return function(str){
            if(str.length >= 80){
                return str.substring(0, 80);
            }
            return str;
        };
    });
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').filter('porcentagem', ["$filter", function ($filter) {
        return function (valor) {
            var valorFormatado = $filter('number')(valor, 2);

            return valorFormatado ? valorFormatado : 0;
        };
    }]);
})();

(function () {
    'use strict';
    angular.module('su-fiscalizacao').filter('mesFilter', function () {
        return function (mes) {
            switch (mes) {
                case 1:
                    return 'Janeiro';
                case 2:
                    return 'Fevereiro';
                case 3:
                    return 'Março';
                case 4:
                    return 'Abril';
                case 5:
                    return 'Maio';
                case 6:
                    return 'Junho';
                case 7:
                    return 'Julho';
                case 8:
                    return 'Agosto';
                case 9:
                    return 'Setembro';
                case 10:
                    return 'Outubro';
                case 11:
                    return 'Novembro';
                case 12:
                    return 'Dezembro';
            }
            return mes;
        };
    });
})();

(function(){
    'use strict';
    angular.module('su-fiscalizacao').filter('numberProcesso', function(){
        return function(number){
            if(!number){
                return '';
            }

            var value = number.toString().trim().replace(/^\+/, '');

            if(value.match(/[^0-9]/)){
                return number;
            }

            var number1, number2, number3, number4;

            switch(value.length){
                case 17: // PP####### -> (PP) ####-####
                    number1 = value.slice(0, 5);
                    number2 = value.slice(5, 11);
                    number3 = value.slice(11, 15);
                    number4 = value.slice(15);
                    break;

                default:
                    return number;
            }

            return number1 + "." + number2 + "/" + number3 + "-" + number4;
        };
    });

    angular.module('su-fiscalizacao').filter('numeroDemanda', function(){
        return function(num){
            var len = 7;
            num = parseInt(num);
            if(!num ){
                return '';
            }
            if (isNaN(num)) {
                return n;
            }
            num = ''+num;
            while (num.length < len) {
                num = '0'+num;
            }
            return num;
        };
    });
})();

(function(){
    'use strict';
    angular.module('su-fiscalizacao').filter('orderbyAccentsName', function(){
        return function(data){
            (data ? data : [] ).sort(function (a, b) {
                return a.nome.localeCompare(b.nome, "standard", { sensitivity: "base" });
            });
            return data;
        };
    });
})();

(function () {
    'use strict';
    angular
        .module('su-fiscalizacao')
        .factory('mFactory', function () {
            var camadas = [];
            var compartilhar = {};
            var mMapFactory;

            var btSetaEsqueda = {};

            var listaAtributos = [];

            function toggle(item) {
                var idx = this.camadas.indexOf(item);
                if (idx > -1) {
                    this.camadas.splice(idx, 1);
                    if (item.layer) {
                        this.mMapFactory.removeLayer(item.layer);
                    }
                } else {
                    this.camadas.unshift(item);
                    if (item.layer) {
                        this.mMapFactory.addLayer(item.layer);
                    }
                }
            }

            function exists(item) {
                return this.camadas.indexOf(item) > -1;
            }

            function getFromUUID(id) {
                for (var i = 0; i < this.camadas.length; i++) {
                    var camada = this.camadas[i];
                    if (camada.id === id) {
                        return camada;
                    }
                }
                return null;
            }

            return {
                camadas: camadas,
                compartilhar: compartilhar,
                mMapFactory: mMapFactory,
                toggle: toggle,
                exists: exists,
                getFromUUID: getFromUUID,
                btSetaEsqueda: btSetaEsqueda,
                listaAtributos: listaAtributos
            };
        });
})();

(function() {
    'use strict';

    angular.module('su-fiscalizacao').directive('integerNumber', function(){
        return {
            require : 'ngModel',
            link : function(scope, element, attrs, ngModel){
                ngModel.$parsers.push(function (data) {
                    if(data === null){
                        data = "";
                    }
                    var number = data.toString().replace(/[^0-9]+/g, ''),
                        thousandSeparator = "",
                        thousandQuantity = (thousandQuantity = number.length) > 3 ? thousandQuantity % 3 : 0;

                    element.val((thousandQuantity ? number.substr(0, thousandQuantity) + thousandSeparator : "") + number.substr(thousandQuantity).replace(/(\d{3})(?=\d)/g, "$1" + thousandSeparator));
                    return number;
                });
            }
        };
    });
})();

(function() {
    'use strict';

    angular.module('su-fiscalizacao').directive('inputHorario', function(){
        return {
            require : 'ngModel',
            link : function(scope, element, attrs, ngModel){

                function formatter(value){
                    if(ngModel.$isEmpty(value)){
                        ngModel.$setValidity("horario", true);
                        return value;
                    }

                    var cleanValue = value.toString();
                    cleanValue = cleanValue.replace(/[^0-9]/g, '');
                    var formatedValue = formatterDate(cleanValue) || '';

                    return formatedValue.trim().replace(/[^0-9]$/, '');
                }

                ngModel.$formatters.push(formatter);

                ngModel.$parsers.push(function parser(value){
                    if(ngModel.$isEmpty(value)){
                        ngModel.$setValidity("horario", true);
                        return value;
                    }

                    var formatedValue = formatter(value);

                    if(ngModel.$viewValue !== formatedValue){
                        ngModel.$setViewValue(formatedValue);
                        ngModel.$render();
                    }

                    return formatedValue;
                });

                function formatterDate(number){
                    var filter;

                    if(number.length >= 2){
                        filter = "99:99";
                        ngModel.$setValidity("horario", number.length >= 4);
                        if(number.substr(0, 2) >= 24){
                            number = '23' + number.substr(2, number.length);
                        }
                        if(number.substr(2, 4) >= 60){
                            number = number.substr(0, 2) + '59';
                        }
                    }else if(number.length < 2){
                        filter = "99";
                        ngModel.$setValidity("horario", false);
                    }

                    var formatter = new StringMask(filter);
                    return formatter.apply(number);
                }
            }
        };
    });
})();

//  https://cdn.gitcdn.link/cdn/angular/bower-material/v1.1.0-rc1/angular-material.js
//  https://github.com/angular/material/blob/master/src/components/datepicker/datePicker.js

(function () {
    'use strict';

    // POST RELEASE
    // TODO(jelbourn): Demo that uses moment.js
    // TODO(jelbourn): make sure this plays well with validation and ngMessages.
    // TODO(jelbourn): calendar pane doesn't open up outside of visible viewport.
    // TODO(jelbourn): forward more attributes to the internal input (required, autofocus, etc.)
    // TODO(jelbourn): something better for mobile (calendar panel takes up entire screen?)
    // TODO(jelbourn): input behavior (masking? auto-complete?)
    // TODO(jelbourn): UTC mode
    // TODO(jelbourn): RTL


    DatePickerCtrl.$inject = ["$scope", "$element", "$attrs", "$compile", "$timeout", "$window", "$mdConstant", "$mdTheming", "$mdUtil", "$mdDateLocale", "$$mdDateUtil", "$$rAF"];
    angular.module('material.components.datepicker')
        .directive('mdDatepickerCustomFiscalizacao', datePickerDirective);

    /**
     * @ngdoc directive
     * @name mdDatepickerCustomFiscalizacao
     * @module material.components.datepicker
     *
     * @param {Date} ng-model The component's model. Expects a JavaScript Date object.
     * @param {expression=} ng-change Expression evaluated when the model value changes.
     * @param {Date=} md-min-date Expression representing a min date (inclusive).
     * @param {Date=} md-max-date Expression representing a max date (inclusive).
     * @param {(function(Date): boolean)=} md-date-filter Function expecting a date and returning a boolean whether it can be selected or not.
     * @param {String=} md-placeholder The date input placeholder value.
     * @param {boolean=} ng-disabled Whether the datepicker is disabled.
     * @param {boolean=} ng-required Whether a value is required for the datepicker.
     *
     * @description
     * `<md-datepicker>` is a component used to select a single date.
     * For information on how to configure internationalization for the date picker,
     * see `$mdDateLocaleProvider`.
     *
     * This component supports [ngMessages](https://docs.angularjs.org/api/ngMessages/directive/ngMessages).
     * Supported attributes are:
     * * `required`: whether a required date is not set.
     * * `mindate`: whether the selected date is before the minimum allowed date.
     * * `maxdate`: whether the selected date is after the maximum allowed date.
     *
     * @usage
     * <hljs lang="html">
     *   <md-datepicker ng-model="birthday"></md-datepicker>
     * </hljs>
     *
     */
    function datePickerDirective() {
        return {
            template:
            // Buttons are not in the tab order because users can open the calendar via keyboard
            // interaction on the text input, and multiple tab stops for one component (picker)
            // may be confusing.
            '<div style="display: flex; flex-direction: row">' +
            '<md-button class="md-datepicker-button md-icon-button" type="button" ' +
            'tabindex="-1" aria-hidden="true" ' +
            'ng-click="ctrl.openCalendarPane($event)">' +
            '<md-icon class="md-datepicker-calendar-icon" md-svg-icon="assets/img/icons/ic_calendar.svg"></md-icon>' +
            '</md-button>' +
            '<div class="md-datepicker-input-container" ' +
            'ng-class="{\'md-datepicker-focused\': ctrl.isFocused}">' +
            '<input class="md-datepicker-input" aria-haspopup="true" ' +
            'ng-focus="ctrl.setFocused(true)" ng-blur="ctrl.setFocused(false)" ng-model="ctrl.defaultDate" mask="{{::ctrl.mask}}" mask-clean="{{::ctrl.cleanMask}}" mask-restrict="{{::ctrl.restrict}}">' +
            '<md-button type="button" md-no-ink ' +
            'class="md-datepicker-triangle-button md-icon-button" ' +
            'ng-click="ctrl.openCalendarPane($event)" ' +
            'aria-label="{{::ctrl.dateLocale.msgOpenCalendar}}">' +
            '<div class="md-datepicker-expand-triangle"></div>' +
            '</md-button>' +
            '</div>' +
            '</div>' +

            // This pane will be detached from here and re-attached to the document body.
            '<div class="md-block validation-messages" flex-gt-xs flex="50" style="color:red; font-size: 12px">' +
            '<div ng-show="ctrl.ngModelCtrl.$error.valida">Escolha uma data válida!</div>' +
            '</div>' +
            '<div class="md-datepicker-calendar-pane md-whiteframe-z1">' +
            '<div class="md-datepicker-input-mask">' +
            '<div class="md-datepicker-input-mask-opaque"></div>' +
            '</div>' +
            '<div class="md-datepicker-calendar">' +
            '<md-calendar role="dialog" aria-label="{{::ctrl.dateLocale.msgCalendar}}" ' +
            'md-min-date="ctrl.minDate" md-max-date="ctrl.maxDate"' +
            'md-date-filter="ctrl.dateFilter"' +
            'ng-model="ctrl.date" ng-if="ctrl.isCalendarOpen">' +
            '</md-calendar>' +
            '</div>' +
            '</div>',
            require: ['ngModel', 'mdDatepickerCustomFiscalizacao', '?^mdInputContainer'],
            scope: {
                minDate: '=mdMinDate',
                maxDate: '=mdMaxDate',
                placeholder: '@mdPlaceholder',
                dateFilter: '=mdDateFilter',
                //  Custom attributes added
                defaultDate: '@mdCustomDefaultDate',
                mask: '@mdCustomMask',
                cleanMask: '@mdCustomCleanMask',
                restrict: '@mdCustomRestrict',
                required: '@mdCustomRequired'
            },
            controller: DatePickerCtrl,
            controllerAs: 'ctrl',
            bindToController: true,
            link: function (scope, element, attr, controllers) {
                var ngModelCtrl = controllers[0];
                var mdDatePickerCtrl = controllers[1];

                var mdInputContainer = controllers[2];
                if (mdInputContainer) {
                    throw Error('md-datepicker should not be placed inside md-input-container.');
                }

                mdDatePickerCtrl.configureNgModel(ngModelCtrl);
            }
        };
    }

    /** Additional offset for the input's `size` attribute, which is updated based on its content. */
    var EXTRA_INPUT_SIZE = 3;

    /** Class applied to the container if the date is invalid. */
    var INVALID_CLASS = 'md-datepicker-invalid';

    /** Default time in ms to debounce input event by. */
    var DEFAULT_DEBOUNCE_INTERVAL = 500;

    /**
     * Height of the calendar pane used to check if the pane is going outside the boundary of
     * the viewport. See calendar.scss for how $md-calendar-height is computed; an extra 20px is
     * also added to space the pane away from the exact edge of the screen.
     *
     *  This is computed statically now, but can be changed to be measured if the circumstances
     *  of calendar sizing are changed.
     */
    var CALENDAR_PANE_HEIGHT = 368;

    /**
     * Width of the calendar pane used to check if the pane is going outside the boundary of
     * the viewport. See calendar.scss for how $md-calendar-width is computed; an extra 20px is
     * also added to space the pane away from the exact edge of the screen.
     *
     *  This is computed statically now, but can be changed to be measured if the circumstances
     *  of calendar sizing are changed.
     */
    var CALENDAR_PANE_WIDTH = 360;

    /**
     * Controller for md-datepicker.
     *
     * @ngInject @constructor
     */
    function DatePickerCtrl($scope, $element, $attrs, $compile, $timeout, $window,
                            $mdConstant, $mdTheming, $mdUtil, $mdDateLocale, $$mdDateUtil, $$rAF) {
        /** @final */
        this.$compile = $compile;

        /** @final */
        this.$timeout = $timeout;

        /** @final */
        this.$window = $window;

        /** @final */
        this.dateLocale = $mdDateLocale;

        /** @final */
        this.dateUtil = $$mdDateUtil;

        /** @final */
        this.$mdConstant = $mdConstant;

        /* @final */
        this.$mdUtil = $mdUtil;

        /** @final */
        this.$$rAF = $$rAF;

        /**
         * The root document element. This is used for attaching a top-level click handler to
         * close the calendar panel when a click outside said panel occurs. We use `documentElement`
         * instead of body because, when scrolling is disabled, some browsers consider the body element
         * to be completely off the screen and propagate events directly to the html element.
         * @type {!angular.JQLite}
         */
        this.documentElement = angular.element(document.documentElement);

        /** @type {!angular.NgModelController} */
        this.ngModelCtrl = null;

        /** @type {HTMLInputElement} */
        this.inputElement = $element[0].querySelector('input');

        /** @final {!angular.JQLite} */
        this.ngInputElement = angular.element(this.inputElement);

        /** @type {HTMLElement} */
        this.inputContainer = $element[0].querySelector('.md-datepicker-input-container');

        /** @type {HTMLElement} Floating calendar pane. */
        this.calendarPane = $element[0].querySelector('.md-datepicker-calendar-pane');

        /** @type {HTMLElement} Calendar icon button. */
        this.calendarButton = $element[0].querySelector('.md-datepicker-button');

        /**
         * Element covering everything but the input in the top of the floating calendar pane.
         * @type {HTMLElement}
         */
        this.inputMask = $element[0].querySelector('.md-datepicker-input-mask-opaque');

        /** @final {!angular.JQLite} */
        this.$element = $element;

        /** @final {!angular.Attributes} */
        this.$attrs = $attrs;

        /** @final {!angular.Scope} */
        this.$scope = $scope;

        /** @type {Date} */
        this.date = null;

        /** @type {boolean} */
        this.isFocused = false;

        /** @type {boolean} */
        this.isDisabled;
        this.setDisabled($element[0].disabled || angular.isString($attrs['disabled']));

        /** @type {boolean} Whether the date-picker's calendar pane is open. */
        this.isCalendarOpen = false;

        /**
         * Element from which the calendar pane was opened. Keep track of this so that we can return
         * focus to it when the pane is closed.
         * @type {HTMLElement}
         */
        this.calendarPaneOpenedFrom = null;

        this.calendarPane.id = 'md-date-pane' + $mdUtil.nextUid();

        $mdTheming($element);

        /** Pre-bound click handler is saved so that the event listener can be removed. */
        this.bodyClickHandler = angular.bind(this, this.handleBodyClick);

        /** Pre-bound resize handler so that the event listener can be removed. */
        this.windowResizeHandler = $mdUtil.debounce(angular.bind(this, this.closeCalendarPane), 100);

        // Unless the user specifies so, the datepicker should not be a tab stop.
        // This is necessary because ngAria might add a tabindex to anything with an ng-model
        // (based on whether or not the user has turned that particular feature on/off).
        if (!$attrs['tabindex']) {
            $element.attr('tabindex', '-1');
        }

        this.installPropertyInterceptors();
        this.attachChangeListeners();
        this.attachInteractionListeners();

        var self = this;
        $scope.$on('$destroy', function () {
            self.detachCalendarPane();
        });
    }

    /**
     * Sets up the controller's reference to ngModelController.
     * @param {!angular.NgModelController} ngModelCtrl
     */
    DatePickerCtrl.prototype.configureNgModel = function (ngModelCtrl) {
        this.ngModelCtrl = ngModelCtrl;

        var self = this;
        ngModelCtrl.$render = function () {
            var value = self.ngModelCtrl.$viewValue;

            if (value && !(value instanceof Date)) {
                throw Error('The ng-model for md-datepicker must be a Date instance. ' +
                    'Currently the model is a: ' + (typeof value));
            }

            self.date = value;
            self.inputElement.value = self.dateLocale.formatDate(value);
            self.resizeInputElement();
            self.updateErrorState();
        };
    };

    /**
     * Attach event listeners for both the text input and the md-calendar.
     * Events are used instead of ng-model so that updates don't infinitely update the other
     * on a change. This should also be more performant than using a $watch.
     */
    DatePickerCtrl.prototype.attachChangeListeners = function () {
        var self = this;

        self.$scope.$on('md-calendar-change', function (event, date) {
            self.ngModelCtrl.$setViewValue(date);
            self.date = date;
            self.inputElement.value = self.dateLocale.formatDate(date);
            self.closeCalendarPane();
            self.resizeInputElement();
            self.updateErrorState();
        });

        self.ngInputElement.on('input', angular.bind(self, self.resizeInputElement));
        // TODO(chenmike): Add ability for users to specify this interval.
        self.ngInputElement.on('input', self.$mdUtil.debounce(self.handleInputEvent,
            DEFAULT_DEBOUNCE_INTERVAL, self));
    };

    /** Attach event listeners for user interaction. */
    DatePickerCtrl.prototype.attachInteractionListeners = function () {
        var self = this;
        var $scope = this.$scope;
        var keyCodes = this.$mdConstant.KEY_CODE;

        // Add event listener through angular so that we can triggerHandler in unit tests.
        self.ngInputElement.on('keydown', function (event) {
            if (event.altKey && event.keyCode == keyCodes.DOWN_ARROW) {
                self.openCalendarPane(event);
                $scope.$digest();
            }
        });

        $scope.$on('md-calendar-close', function () {
            self.closeCalendarPane();
        });
    };

    /**
     * Capture properties set to the date-picker and imperitively handle internal changes.
     * This is done to avoid setting up additional $watches.
     */
    DatePickerCtrl.prototype.installPropertyInterceptors = function () {
        var self = this;

        if (this.$attrs['ngDisabled']) {
            // The expression is to be evaluated against the directive element's scope and not
            // the directive's isolate scope.
            var scope = this.$scope.$parent;

            if (scope) {
                scope.$watch(this.$attrs['ngDisabled'], function (isDisabled) {
                    self.setDisabled(isDisabled);
                });
            }
        }

        Object.defineProperty(this, 'placeholder', {
            get: function () {
                return self.inputElement.placeholder;
            },
            set: function (value) {
                self.inputElement.placeholder = value || '';
            }
        });
    };

    /**
     * Sets whether the date-picker is disabled.
     * @param {boolean} isDisabled
     */
    DatePickerCtrl.prototype.setDisabled = function (isDisabled) {
        this.isDisabled = isDisabled;
        this.inputElement.disabled = isDisabled;
        this.calendarButton.disabled = isDisabled;
    };

    /**
     * Sets the custom ngModel.$error flags to be consumed by ngMessages. Flags are:
     *   - mindate: whether the selected date is before the minimum date.
     *   - maxdate: whether the selected flag is after the maximum date.
     *   - filtered: whether the selected date is allowed by the custom filtering function.
     *   - valid: whether the entered text input is a valid date
     *
     * The 'required' flag is handled automatically by ngModel.
     *
     * @param {Date=} opt_date Date to check. If not given, defaults to the datepicker's model value.
     */
    DatePickerCtrl.prototype.updateErrorState = function (opt_date) {
        var date = opt_date || this.date;

        // Clear any existing errors to get rid of anything that's no longer relevant.
        this.clearErrorState();

        if (this.dateUtil.isValidDate(date)) {
            // Force all dates to midnight in order to ignore the time portion.
            date = this.dateUtil.createDateAtMidnight(date);

            if (this.dateUtil.isValidDate(this.minDate)) {
                var minDate = this.dateUtil.createDateAtMidnight(this.minDate);
                this.ngModelCtrl.$setValidity('mindate', date >= minDate);
            }

            if (this.dateUtil.isValidDate(this.maxDate)) {
                var maxDate = this.dateUtil.createDateAtMidnight(this.maxDate);
                this.ngModelCtrl.$setValidity('maxdate', date <= maxDate);
            }

            if (angular.isFunction(this.dateFilter)) {
                this.ngModelCtrl.$setValidity('filtered', this.dateFilter(date));
            }
        } else {
            // The date is seen as "not a valid date" if there is *something* set
            // (i.e.., not null or undefined), but that something isn't a valid date.
            //  this.ngModelCtrl.$setValidity('valid', date == null);
            this.ngModelCtrl.$setValidity('valid', !isNaN(date));
            this.ngModelCtrl.$setValidity('required', this.required !== "true" || date !== null);
        }

        // TODO(jelbourn): Change this to classList.toggle when we stop using PhantomJS in unit tests
        // because it doesn't conform to the DOMTokenList spec.
        // See https://github.com/ariya/phantomjs/issues/12782.
        if (!this.ngModelCtrl.$valid) {
            this.inputContainer.classList.add(INVALID_CLASS);
        }
    };

    /** Clears any error flags set by `updateErrorState`. */
    DatePickerCtrl.prototype.clearErrorState = function () {
        this.inputContainer.classList.remove(INVALID_CLASS);
        ['mindate', 'maxdate', 'filtered', 'valid', 'required'].forEach(function (field) {
            this.ngModelCtrl.$setValidity(field, true);
        }, this);
    };

    /** Resizes the input element based on the size of its content. */
    DatePickerCtrl.prototype.resizeInputElement = function () {
        this.inputElement.size = this.inputElement.value.length + EXTRA_INPUT_SIZE;
    };

    /**
     * Sets the model value if the user input is a valid date.
     * Adds an invalid class to the input element if not.
     */
    DatePickerCtrl.prototype.handleInputEvent = function () {
        var inputString = this.inputElement.value;
        var parsedDate = inputString ? this.dateLocale.parseDate(inputString) : null;
        this.dateUtil.setDateTimeToMidnight(parsedDate);

        // An input string is valid if it is either empty (representing no date)
        // or if it parses to a valid date that the user is allowed to select.
        var isValidInput = inputString == '' || (
            this.dateUtil.isValidDate(parsedDate) &&
            this.dateLocale.isDateComplete(inputString) &&
            this.isDateEnabled(parsedDate)
        );

        // The datepicker's model is only updated when there is a valid input.
        if (isValidInput) {
            this.ngModelCtrl.$setViewValue(parsedDate);
            this.date = parsedDate;
        }

        var from = (inputString || "").split("/");
        if(from.length == 3 && from[2].length == 4){
            var f = this.isValidDate(from[1]+"/"+from[0]+"/"+from[2]);
            if (f) {
                this.ngModelCtrl.$setValidity('valida', true);
            } else {
                this.ngModelCtrl.$setValidity('valida', false);
            }
        } else {
            parsedDate = null;
            this.ngModelCtrl.$setValidity('valida', true);
        }

        this.updateErrorState(parsedDate);
    };

    DatePickerCtrl.prototype.isValidDate = function (dateStr) {

        // Checks for the following valid date formats:
        // MM/DD/YYYY
        // Also separates date into month, day, and year variables
        var datePat = /^(\d{2,2})(\/)(\d{2,2})\2(\d{4}|\d{4})$/;

        var matchArray = dateStr.match(datePat); // is the format ok?
        if (matchArray == null) {
            return false;
        }

        var month = matchArray[1]; // parse date into variables
        var day = matchArray[3];
        var year = matchArray[4];
        if (month < 1 || month > 12) { // check month range
            return false;
        }
        if (day < 1 || day > 31) {
            return false;
        }
        if ((month==4 || month==6 || month==9 || month==11) && day==31) {
            return false;
        }
        if (month == 2) { // check for february 29th
            var isleap = (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0));
            if (day>29 || (day==29 && !isleap)) {
                return false;
            }
        }
        return true;  // date is valid
    };

    /**
     * Check whether date is in range and enabled
     * @param {Date=} opt_date
     * @return {boolean} Whether the date is enabled.
     */
    DatePickerCtrl.prototype.isDateEnabled = function (opt_date) {
        return this.dateUtil.isDateWithinRange(opt_date, this.minDate, this.maxDate) &&
            (!angular.isFunction(this.dateFilter) || this.dateFilter(opt_date));
    };

    /** Position and attach the floating calendar to the document. */
    DatePickerCtrl.prototype.attachCalendarPane = function () {
        var calendarPane = this.calendarPane;
        calendarPane.style.transform = '';
        this.$element.addClass('md-datepicker-open');

        var elementRect = this.inputContainer.getBoundingClientRect();
        var bodyRect = document.body.getBoundingClientRect();

        // Check to see if the calendar pane would go off the screen. If so, adjust position
        // accordingly to keep it within the viewport.
        var paneTop = elementRect.top - bodyRect.top;
        var paneLeft = elementRect.left - bodyRect.left;

        // If ng-material has disabled body scrolling (for example, if a dialog is open),
        // then it's possible that the already-scrolled body has a negative top/left. In this case,
        // we want to treat the "real" top as (0 - bodyRect.top). In a normal scrolling situation,
        // though, the top of the viewport should just be the body's scroll position.
        var viewportTop = (bodyRect.top < 0 && document.body.scrollTop == 0) ?
            -bodyRect.top :
            document.body.scrollTop;

        var viewportLeft = (bodyRect.left < 0 && document.body.scrollLeft == 0) ?
            -bodyRect.left :
            document.body.scrollLeft;

        var viewportBottom = viewportTop + this.$window.innerHeight;
        var viewportRight = viewportLeft + this.$window.innerWidth;

        // If the right edge of the pane would be off the screen and shifting it left by the
        // difference would not go past the left edge of the screen. If the calendar pane is too
        // big to fit on the screen at all, move it to the left of the screen and scale the entire
        // element down to fit.
        if (paneLeft + CALENDAR_PANE_WIDTH > viewportRight) {
            if (viewportRight - CALENDAR_PANE_WIDTH > 0) {
                paneLeft = viewportRight - CALENDAR_PANE_WIDTH;
            } else {
                paneLeft = viewportLeft;
                var scale = this.$window.innerWidth / CALENDAR_PANE_WIDTH;
                calendarPane.style.transform = 'scale(' + scale + ')';
            }

            calendarPane.classList.add('md-datepicker-pos-adjusted');
        }

        // If the bottom edge of the pane would be off the screen and shifting it up by the
        // difference would not go past the top edge of the screen.
        if (paneTop + CALENDAR_PANE_HEIGHT > viewportBottom &&
            viewportBottom - CALENDAR_PANE_HEIGHT > viewportTop) {
            paneTop = viewportBottom - CALENDAR_PANE_HEIGHT;
            calendarPane.classList.add('md-datepicker-pos-adjusted');
        }

        calendarPane.style.left = paneLeft + 'px';
        calendarPane.style.top = paneTop + 'px';
        document.body.appendChild(calendarPane);

        // The top of the calendar pane is a transparent box that shows the text input underneath.
        // Since the pane is floating, though, the page underneath the pane *adjacent* to the input is
        // also shown unless we cover it up. The inputMask does this by filling up the remaining space
        // based on the width of the input.
        this.inputMask.style.left = elementRect.width + 'px';

        // Add CSS class after one frame to trigger open animation.
        this.$$rAF(function () {
            calendarPane.classList.add('md-pane-open');
        });
    };

    /** Detach the floating calendar pane from the document. */
    DatePickerCtrl.prototype.detachCalendarPane = function () {
        this.$element.removeClass('md-datepicker-open');
        this.calendarPane.classList.remove('md-pane-open');
        this.calendarPane.classList.remove('md-datepicker-pos-adjusted');

        if (this.isCalendarOpen) {
            this.$mdUtil.enableScrolling();
        }

        if (this.calendarPane.parentNode) {
            // Use native DOM removal because we do not want any of the angular state of this element
            // to be disposed.
            this.calendarPane.parentNode.removeChild(this.calendarPane);
        }
    };

    /**
     * Open the floating calendar pane.
     * @param {Event} event
     */
    DatePickerCtrl.prototype.openCalendarPane = function (event) {
        if (!this.isCalendarOpen && !this.isDisabled) {
            this.isCalendarOpen = true;
            this.calendarPaneOpenedFrom = event.target;

            // Because the calendar pane is attached directly to the body, it is possible that the
            // rest of the component (input, etc) is in a different scrolling container, such as
            // an md-content. This means that, if the container is scrolled, the pane would remain
            // stationary. To remedy this, we disable scrolling while the calendar pane is open, which
            // also matches the native behavior for things like `<select>` on Mac and Windows.
            this.$mdUtil.disableScrollAround(this.calendarPane);

            this.attachCalendarPane();
            this.focusCalendar();

            // Attach click listener inside of a timeout because, if this open call was triggered by a
            // click, we don't want it to be immediately propogated up to the body and handled.
            var self = this;
            this.$mdUtil.nextTick(function () {
                // Use 'touchstart` in addition to click in order to work on iOS Safari, where click
                // events aren't propogated under most circumstances.
                // See http://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html
                self.documentElement.on('click touchstart', self.bodyClickHandler);
            }, false);

            window.addEventListener('resize', this.windowResizeHandler);
        }
    };

    /** Close the floating calendar pane. */
    DatePickerCtrl.prototype.closeCalendarPane = function () {
        if (this.isCalendarOpen) {
            this.detachCalendarPane();
            this.isCalendarOpen = false;
            this.calendarPaneOpenedFrom.focus();
            this.calendarPaneOpenedFrom = null;

            this.ngModelCtrl.$setTouched();

            this.documentElement.off('click touchstart', this.bodyClickHandler);
            window.removeEventListener('resize', this.windowResizeHandler);
        }
    };

    /** Gets the controller instance for the calendar in the floating pane. */
    DatePickerCtrl.prototype.getCalendarCtrl = function () {
        return angular.element(this.calendarPane.querySelector('md-calendar')).controller('mdCalendar');
    };

    /** Focus the calendar in the floating pane. */
    DatePickerCtrl.prototype.focusCalendar = function () {
        // Use a timeout in order to allow the calendar to be rendered, as it is gated behind an ng-if.
        var self = this;
        this.$mdUtil.nextTick(function () {
            /**
             * Removido o .focus() estava com erro de is not a function, original self.getCalendarCtrl().focus();
             */
            self.getCalendarCtrl();
        }, false);
    };

    /**
     * Sets whether the input is currently focused.
     * @param {boolean} isFocused
     */
    DatePickerCtrl.prototype.setFocused = function (isFocused) {
        if (!isFocused) {
            this.ngModelCtrl.$setTouched();
            var from = (this.inputElement.value || "").split("/");
            if(!(from.length == 3 && from[2].length == 4)){
                this.inputElement.value = null;
            }
        }
        this.isFocused = isFocused;
    };

    /**
     * Handles a click on the document body when the floating calendar pane is open.
     * Closes the floating calendar pane if the click is not inside of it.
     * @param {MouseEvent} event
     */
    DatePickerCtrl.prototype.handleBodyClick = function (event) {
        if (this.isCalendarOpen) {
            // TODO(jelbourn): way want to also include the md-datepicker itself in this check.
            var isInCalendar = this.$mdUtil.getClosest(event.target, 'md-calendar');
            if (!isInCalendar) {
                this.closeCalendarPane();
            }

            this.$scope.$digest();
        }
    };
})();

//angular
//    .module('MyApp', ['ngMaterial', 'ngMessages', 'angularMoment', 'ngMask'])
//    .config(function ($mdDateLocaleProvider, moment) {
//
//        $mdDateLocaleProvider.formatDate = function(date) {
//            return moment(date).format('MM/DD/YYYY');
//        };
//
//        $mdDateLocaleProvider.parseDate = function(dateString) {
//            if (dateString == null || dateString.length == 0) {
//                return null;
//            }
//
//            var m = moment(dateString, 'MM/DD/YYYY', true);
//            return m.isValid() ? m.toDate() : new Date(NaN);
//        }
//
//    })
//    .controller('AppController', function (moment) {
//        var appCtrl = this,
//            dateMaskFormat = 'MM/DD/YYYY';
//
//        appCtrl.myDate = new Date();
//        appCtrl.defaultDate = moment(appCtrl.myDate).format(dateMaskFormat);
//        appCtrl.minDate = moment(appCtrl.myDate).subtract(5, 'years').toDate();
//        appCtrl.maxDate = moment(appCtrl.myDate).add(5, 'years').toDate();
//    });

(function () {
    'use strict';

    angular.module('su-fiscalizacao')
        .value('urlPrefixo', '/fiscalizacao')
        .config(["$stateProvider", function ($stateProvider) {

            $stateProvider
                .state('fiscalizacao', {
                    url: '/fiscalizacao',
                    template: '<div id="su-fiscalizacao" ui-view autoscroll="true"> </div>',
                    abstract: true
                })

                //Demanda
                .state('fiscalizacao.demanda', {
                    url: '/demanda',
                    abstract: true
                })

                //Listar Demanda
                .state('fiscalizacao.demanda.listar', {
                    url: '/listar',
                    templateUrl: 'scripts-fiscalizacao/pages/demanda/listar/view/demanda-listar.tmpl.html',
                    controller: 'DemandaListarController as scope',
                    ncyBreadcrumb: {
                        label: 'LISTAR DEMANDAS'
                    }
                })

                //Incluir Demanda
                .state('fiscalizacao.demanda.incluir', {
                    url: '/incluir',
                    templateUrl: 'scripts-fiscalizacao/pages/demanda/detalhar/views/demanda-detalhar.tmpl.html',
                    controller: 'DemandaDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'INCLUIR DADOS DA DEMANDA',
                        parent: 'fiscalizacao.demanda.listar'
                    }
                })

                //Incluir Demanda no cadastro de relatório de fiscalização
                .state('fiscalizacao.demanda.demandaOf', {
                    url: '/demandaOf/:ofId',
                    templateUrl: 'scripts-fiscalizacao/pages/demanda/detalhar/views/demanda-detalhar.tmpl.html',
                    controller: 'DemandaDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'INCLUIR DADOS DA DEMANDA',
                        parent: 'fiscalizacao.demanda.listar'
                    }
                })

                //Consultar Demanda
                .state('fiscalizacao.demanda.consultar', {
                    url: '/consultar/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/demanda/detalhar/views/demanda-detalhar.tmpl.html',
                    controller: 'DemandaDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'VISUALIZAR DADOS DA DEMANDA',
                        parent: 'fiscalizacao.demanda.listar'
                    }
                })

                //Alterar Demanda
                .state('fiscalizacao.demanda.alterar', {
                    url: '/alterar/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/demanda/detalhar/views/demanda-detalhar.tmpl.html',
                    controller: 'DemandaDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'ALTERAR DADOS DA DEMANDA',
                        parent: 'fiscalizacao.demanda.listar'
                    }
                })
                //Mapa Demanda
                .state('fiscalizacao.demanda.mapa', {
                    url: '/mapa',
                    templateUrl: 'scripts-fiscalizacao/pages/demanda/mapa/view/demanda-mapa.tmpl.html',
                    controller: 'DemandaMapaController as scope',
                    ncyBreadcrumb: {
                        label: 'MAPA DADOS DA DEMANDA',
                        parent: 'fiscalizacao.demanda.listar'
                    }
                })
                //Ordem Fiscalização
                .state('fiscalizacao.ordemFiscalizacao', {
                    url: '/ordemFiscalizacao',
                    abstract: true
                })
                //Listar Ordem Fiscalização
                .state('fiscalizacao.ordemFiscalizacao.listar', {
                    url: '/listar',
                    templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/listar/view/ordemFiscalizacao-listar.tmpl.html',
                    controller: 'OrdemFiscalizacaoListarController as scope',
                    ncyBreadcrumb: {
                        label: 'LISTAR ORDENS DE FISCALIZAÇÃO'
                    }
                })
                //Incluir Ordem Fiscalização
                .state('fiscalizacao.ordemFiscalizacao.incluir', {
                    url: '/incluir',
                    templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/detalhar/views/ordemFiscalizacao-detalhar.tmpl.html',
                    controller: 'OrdemFiscalizacaoDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'INCLUIR ORDEM DE FISCALIZAÇÃO',
                        parent: 'fiscalizacao.ordemFiscalizacao.listar'
                    }
                })

                //Consultar Ordem Fiscalização
                .state('fiscalizacao.ordemFiscalizacao.consultar', {
                    url: '/visualizar/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/detalhar/views/ordemFiscalizacao-detalhar.tmpl.html',
                    controller: 'OrdemFiscalizacaoDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'VISUALIZAR ORDEM DE FISCALIZAÇÃO',
                        parent: 'fiscalizacao.ordemFiscalizacao.listar'
                    }
                })

                //Validar Ordem Fiscalização
                .state('fiscalizacao.ordemFiscalizacao.validar', {
                    url: '/validar/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/detalhar/views/ordemFiscalizacao-detalhar.tmpl.html',
                    controller: 'OrdemFiscalizacaoDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'VALIDAR ORDEM DE FISCALIZAÇÃO',
                        parent: 'fiscalizacao.ordemFiscalizacao.listar'
                    }
                })

                //Revalidar Ordem Fiscalização
                .state('fiscalizacao.ordemFiscalizacao.revalidar', {
                    url: '/revalidar/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/detalhar/views/ordemFiscalizacao-detalhar.tmpl.html',
                    controller: 'OrdemFiscalizacaoDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'REVALIDAR ORDEM DE FISCALIZAÇÃO',
                        parent: 'fiscalizacao.ordemFiscalizacao.listar'
                    }
                })

                //Alterar Ordem Fiscalização
                .state('fiscalizacao.ordemFiscalizacao.alterar', {
                    url: '/alterar/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/detalhar/views/ordemFiscalizacao-detalhar.tmpl.html',
                    controller: 'OrdemFiscalizacaoDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'ALTERAR ORDEM DE FISCALIZAÇÃO',
                        parent: 'fiscalizacao.ordemFiscalizacao.listar'
                    }
                })

                //Incluir Ordem Fiscalização via Mapa
                .state('fiscalizacao.ordemFiscalizacao.mapa', {
                    url: '/mapa',
                    templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/detalhar/views/ordemFiscalizacao-detalhar.tmpl.html',
                    controller: 'OrdemFiscalizacaoDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'INCLUIR ORDEM DE FISCALIZAÇÃO VIA MAPA',
                        parent: 'fiscalizacao.ordemFiscalizacao.listar'
                    }
                })
                //Incluir Fiscalização Executada por Agente Externo
                //Incluir Ordem Fiscalização
                .state('fiscalizacao.ordemFiscalizacao.incluirAgenteExterno', {
                    url: '/incluirAgenteExterno',
                    templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/agenteExterno/views/agenteExterno-detalhar.tmpl.html',
                    controller: 'AgenteExternoDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'INCLUIR FISCALIZAÇÃO EXECUTADA POR AGENTE EXTERNO',
                        parent: 'fiscalizacao.ordemFiscalizacao.listar'
                    }
                })

                //Alterar Ordem Fiscalização
                .state('fiscalizacao.ordemFiscalizacao.alterarAgenteExterno', {
                    url: '/alterarAgenteExterno/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/agenteExterno/views/agenteExterno-detalhar.tmpl.html',
                    controller: 'AgenteExternoDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'ALTERAR FISCALIZAÇÃO EXECUTADA POR AGENTE EXTERNO',
                        parent: 'fiscalizacao.ordemFiscalizacao.listar'
                    }
                })

                //Consultar Ordem Fiscalização
                .state('fiscalizacao.ordemFiscalizacao.consultarAgenteExterno', {
                    url: '/consultarAgenteExterno/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/ordemFiscalizacao/agenteExterno/views/agenteExterno-detalhar.tmpl.html',
                    controller: 'AgenteExternoDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'VISUALIZAR FISCALIZAÇÃO EXECUTADA POR AGENTE EXTERNO',
                        parent: 'fiscalizacao.ordemFiscalizacao.listar'
                    }
                })

                //Relatório
                .state('fiscalizacao.relatorio', {
                    url: '/relatorio',
                    abstract: true
                })
                //Incluir Relatório
                .state('fiscalizacao.relatorio.incluir', {
                    url: '/incluir',
                    templateUrl: 'scripts-fiscalizacao/pages/relatorio/detalhar/view/relatorio-detalhar.tmpl.html',
                    controller: 'RelatorioDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'RELATÓRIO DE FISCALIZAÇÃO'
                    }
                })
                //Listar Relatório
                .state('fiscalizacao.relatorio.listar', {
                    url: '/listar',
                    templateUrl: 'scripts-fiscalizacao/pages/relatorio/listar/view/relatorio-listar.tmpl.html',
                    controller: 'RelatorioListarController as scope',
                    ncyBreadcrumb: {
                        label: 'RELATÓRIO DE FISCALIZAÇÃO'
                    }
                })
                //Alterar Relatório
                .state('fiscalizacao.relatorio.alterar', {
                    url: '/alterar/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/relatorio/detalhar/view/relatorio-detalhar.tmpl.html',
                    controller: 'RelatorioDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'RELATÓRIO DE FISCALIZAÇÃO',
                        parent: 'fiscalizacao.relatorio.listar'
                    },
                    params: {
                        focarAbaAutuado: false
                    }
                })
                //Visualisar Relatório
                .state('fiscalizacao.relatorio.consultar', {
                    url: '/visualizar/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/relatorio/detalhar/view/relatorio-detalhar.tmpl.html',
                    controller: 'RelatorioDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'RELATÓRIO DE FISCALIZAÇÃO',
                        parent: 'fiscalizacao.relatorio.listar'
                    }
                })
                //Consultar Ordem Fiscalização
                .state('fiscalizacao.relatorio.validar', {
                    url: '/validar/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/relatorio/detalhar/view/relatorio-detalhar.tmpl.html',
                    controller: 'RelatorioDetalharController as scope',
                    ncyBreadcrumb: {
                        label: 'VALIDAR RELATÓRIO DE FISCALIZAÇÃO',
                        parent: 'fiscalizacao.ordemFiscalizacao.listar'
                    }
                })
                //Exportar Relatorio Fiscalização
                .state('fiscalizacao.relatorio.exportar', {
                    url: '/exportar',
                    templateUrl: 'scripts-fiscalizacao/pages/relatorio/exportar-relatorio/view/relatorio-exportar.tmpl.html',
                    controller: 'RelatorioExportarController as scope',
                    ncyBreadcrumb: {
                        label: 'EXPORTAR RELATÓRIO DE FISCALIZAÇÃO'
                    }
                })

                //Painel Gerencial
                .state('fiscalizacao.painel', {
                    url: '/painel',
                    abstract: true
                })
                //Relatório(Manter Metas)
                .state('fiscalizacao.painel.meta', {
                    url: '/meta',
                    templateUrl: 'scripts-fiscalizacao/pages/painel/meta/view/meta.tmpl.html',
                    controller: 'MetaController as scope',
                    ncyBreadcrumb: {
                        label: 'Manter Metas'
                    }
                })
                //Relatório(Total de Fiscalização por UF)
                .state('fiscalizacao.painel.total-fiscalizacao', {
                    url: '/total-fiscalizacao',
                    templateUrl: 'scripts-fiscalizacao/pages/painel/total-fiscalizacao/view/total-fiscalizacao.tmpl.html',
                    controller: 'TotalFiscalizacaoController as scope',
                    ncyBreadcrumb: {
                        label: 'Gráfico do Total de Fiscalização por UF'
                    }
                })
                //Relatório(Painel Gerencial)
                .state('fiscalizacao.painel.gerencial', {
                    url: '/gerencial',
                    templateUrl: 'scripts-fiscalizacao/pages/painel/painel-gerencial/view/painel-gerencial.tmpl.html',
                    controller: 'PainelGerencialController as scope',
                    ncyBreadcrumb: {
                        label: 'Painel Gerencial'
                    }
                })
                //Relatório(Painel de Sanções)
                .state('fiscalizacao.painel.sancoes', {
                    url: '/sancoes',
                    templateUrl: 'scripts-fiscalizacao/pages/painel/total-sancao/view/total-sancao.tmpl.html',
                    controller: 'TotalSancaoController as scope',
                    ncyBreadcrumb: {
                        label: 'Painel de Sanções'
                    }
                })

                //Painel Gerenciador de Infração
                .state('fiscalizacao.gerenciadorInfracao', {
                    url: '/gerenciadorInfracao',
                    abstract: true
                })
                //Entrega de Documentos
                .state('fiscalizacao.gerenciadorInfracao.entregaDocumento', {
                    url: '/entregaDocumento',
                    templateUrl: 'scripts-fiscalizacao/pages/gerenciadorInfracao/entregaDocumento/view/entrega-documento.tmpl.html',
                    controller: 'EntregaDocumentoController as scope',
                    ncyBreadcrumb: {
                        label: 'Entrega de Dcoumentos'
                    }
                })
                .state('fiscalizacao.gerenciadorInfracao.recurso', {
                    url: '/recurso',
                    templateUrl: 'scripts-fiscalizacao/pages/gerenciadorInfracao/gerenciarRecurso/view/recurso.tmpl.html',
                    controller: 'RecursoController as scope',
                    ncyBreadcrumb: {
                        label: 'Gerenciar Recurso'
                    }
                })
                .state('fiscalizacao.gerenciadorInfracao.sancao', {
                    url: '/sancao',
                    templateUrl: 'scripts-fiscalizacao/pages/gerenciadorInfracao/sancao/view/sancao.tmpl.html',
                    controller: 'SancaoController as scope',
                    ncyBreadcrumb: {
                        label: 'Consultar Sanção'
                    }
                })
                .state('fiscalizacao.gerenciadorInfracao.detalharSancao', {
                    url: '/detalharSancao/:id',
                    templateUrl: 'scripts-fiscalizacao/pages/gerenciadorInfracao/sancao/detalheSansao/view/detalhe-sancao.html',
                    controller: 'DetalheSancaoController as scope',
                    ncyBreadcrumb: {
                        label: 'Detalhar Sanção'
                    }
                })
                .state('fiscalizacao.gerenciadorInfracao.suspenderCobranca', {
                    url: '/suspenderCobranca/:documentoId',
                    templateUrl: 'scripts-fiscalizacao/pages/gerenciadorInfracao/sancao/suspenderCobranca/view/suspender-cobranca.html',
                    controller: 'SuspenderCobrancaController as scope',
                    ncyBreadcrumb: {
                        label: 'Suspender Cobrança'
                    }
                })
                .state('fiscalizacao.gerenciadorInfracao.detalharCobranca', {
                    url: '/detalharCobranca/:id/:documentoId',
                    templateUrl: 'scripts-fiscalizacao/pages/gerenciadorInfracao/sancao/suspenderCobranca/view/suspender-cobranca.html',
                    controller: 'SuspenderCobrancaController as scope',
                    ncyBreadcrumb: {
                        label: 'Suspender Cobrança'
                    }
                })
                .state('fiscalizacao.gerenciadorInfracao.suspenderNotificacao', {
                    url: '/suspenderNotificacao/:documentoId/:demandaId',
                    templateUrl: 'scripts-fiscalizacao/pages/gerenciadorInfracao/sancao/suspenderNotificacao/view/suspender-notificacao.tmpl.html',
                    controller: 'SuspenderNotificacaoController as scope',
                    ncyBreadcrumb: {
                        label: 'Suspender Notificação'
                    }
                });
        }]);

})();

(function () {
    'use strict';
    /*global angular*/
    angular.module('su-fiscalizacao')
        .constant('CONSTANTS', {
            NOTIFICADORES: {
                DOCUMENTO_MODAL: 'documentoModal'
            },
            TIPO_GRAFICO: {
                CARACTERIZACAO_IMOVEIS_FISCALIZADOS: "Caracterização dos Imóveis Fiscalizados",
                DEMANDAS: "Demandas",
                NATUREZA_IMOVEL: "Natureza do imóvel",
                PLANO_ANUAL_FISCALIZACAO: "Plano Anual de Fiscalização",
                DEMANDAS_MAIS_EXECUTADAS: "Demandas mais executadas",
                FISCALIZACOES_BRASIL: "Fiscalizações no Brasil"
            }
        })
}());

angular.module("su-fiscalizacao").run(["$templateCache", function($templateCache) {$templateCache.put("scripts-fiscalizacao/app/directives/grafico-legenda/grafico.legenda.tmpl.html","<div id={{identificador}} style=\"background-color: #FAFAFA\"><div layout=row layout-xs=column ng-show=grafico.data.length><div class=md-block flex-gt-xs flex=100><h3>{{tituloGrafico}}</h3></div></div><div layout=row layout-xs=column ng-show=grafico.data.length><div class=md-block flex-gt-xs flex=100 ng-if=\"identificador == \'caracterizacao\' || identificador ==\'motivacao\'\"><div layout=row layout-xs=column><div class=md-block flex=10 flex-xs=100><h2 class=quantidade-infracao>Total - {{total}}</h2></div><div class=md-block flex=30 flex-md=50 flex-xs=100 style=\"align-self: center\"><canvas id=grafico{{identificador}} height=300px class=\"chart chart-pie\" chart-options=grafico.options chart-data=grafico.data chart-labels=grafico.labels chart-colors=grafico.colors chart-series=grafico.series ng-show=\"tipo == \'chart-pie\'\"></canvas></div><div class=md-block flex=10 flex-xs=100></div><div id=legenda{{identificador}} class=md-block flex=50 flex-md=50 flex-xs=100 layout=row style=\"display: flex; flex-direction: column\"><div ng-repeat=\"item in grafico.series track by $index\" style=\"margin: 20px 0px 10px 10px\"><div class=md-block flex-gt-xs flex=100><span class=nivel-legenda ng-style=\"{\'background\' : grafico.colors[$index]}\"></span> <span class=nivel-legenda-title>{{$index + 1}} - {{item}} (<strong>{{grafico.data[$index]}}</strong>)</span></div></div></div></div><div layout=row layout-xs=column class=\"buttons-action no-border\" ng-show=grafico.data.length><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=showAlertCaractMotiv($event)><span>Ver Gráfico por UFs</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=gerarPng()><span>PNG</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=downloadPDF()><span>PDF</span></md-button></div></div></div></div><div class=md-block flex-gt-xs flex=100 ng-if=\"identificador == \'natureza\'\"><div layout=row layout-xs=column><div class=md-block flex=50 flex-md=50 flex-xs=100 style=\"align-self: center\"><canvas id=grafico{{identificador}} height=300px class=\"chart chart-pie\" chart-options=grafico.options chart-data=grafico.data chart-labels=grafico.labels chart-colors=grafico.colors chart-series=grafico.series ng-show=\"tipo == \'chart-pie\'\"></canvas></div><div class=md-block flex=10 flex-xs=100></div><div class=md-block flex=40 flex-md=40 flex-xs=100 layout=row style=\"flex-wrap: wrap; justify-content: center\"><div class=md-block flex=100 flex-xs=100><h2 class=quantidade-infracao>Total - {{total}}</h2></div><div id=legenda{{identificador}} class=md-block flex=100><div class=grafico-option-flex ng-repeat=\"item in grafico.series track by $index\" style=\"margin: 20px 0px 10px 10px\"><div class=md-block flex-gt-xs flex=100><span class=nivel-legenda ng-style=\"{\'background\' : grafico.colors[$index]}\"></span> <span class=nivel-legenda-title>{{$index + 1}} - {{item}} (<strong>{{grafico.data[$index]}}</strong>)</span></div></div></div><div flex=100 ng-show=grafico.data.length><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=showAlertNaturezaAnual($event)><span>Ver Gráfico por UFs</span></md-button></div><div flex=100 ng-show=grafico.data.length><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=gerarPng()><span>PNG</span></md-button></div><div flex=100 ng-show=grafico.data.length><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=downloadPDF()><span>PDF</span></md-button></div></div></div></div><div class=md-block flex-gt-xs flex=100 ng-if=\"identificador ==\'Anual\'\"><div layout=row layout-xs=column><div class=md-block flex=50 flex-md=50 flex-xs=100 style=\"align-self: center\"><canvas id=grafico{{identificador}} height=300px class=\"chart chart-doughnut\" chart-options=grafico.options chart-data=grafico.data chart-labels=grafico.labels chart-colors=grafico.colors chart-series=grafico.series ng-show=\"tipo == \'chart-doughnut\'\"></canvas></div><div class=md-block flex=10 flex-xs=100></div><div class=md-block flex=40 flex-md=40 flex-xs=100 layout=row style=\"flex-wrap: wrap; justify-content: center\"><div class=md-block flex=100 flex-xs=100><h2 class=quantidade-infracao>Total - {{total}}</h2></div><div id=legenda{{identificador}} class=md-block flex=100><div class=grafico-option-flex ng-repeat=\"item in grafico.series track by $index\" style=\"margin: 20px 0px 10px 10px\"><div class=md-block flex-gt-xs flex=100><span class=nivel-legenda ng-style=\"{\'background\' : grafico.colors[$index]}\"></span> <span class=nivel-legenda-title>{{$index + 1}} - {{item}} (<strong>{{grafico.data[$index]}}</strong>)</span></div></div></div><div flex=100 ng-show=grafico.data.length><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=showAlertNaturezaAnual($event)><span>Ver Gráfico por UFs</span></md-button></div><div flex=100 ng-show=grafico.data.length><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=gerarPng()><span>PNG</span></md-button></div><div flex=100 ng-show=grafico.data.length><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=downloadPDF()><span>PDF</span></md-button></div></div></div></div><div class=md-block flex-gt-xs flex=100 ng-if=\"identificador == \'assunto\'\"><div class=md-block flex=100 flex-xs=100><h2 class=quantidade-infracao>Total - {{total}}</h2></div><div class=md-block flex=100 flex-md=100 flex-xs=100 style=\"align-self: center\"><canvas id=grafico{{identificador}} height=400px width=500px class=\"chart chart-bar\" chart-options=grafico.options chart-data=grafico.data chart-labels=grafico.labels chart-colors=grafico.colors chart-series=grafico.series chart-options=options ng-show=\"tipo == \'chart-bar\'\"></canvas></div><div layout=row layout-xs=column flex=100 flex-xs=100 class=\"buttons-action no-border\" ng-show=grafico.data.length><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=showAlertAssunto($event)><span>Ver Gráfico por UFs</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=gerarPng()><span>PNG</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=downloadPDF()><span>PDF</span></md-button></div></div></div></div></div></div>");
$templateCache.put("scripts-fiscalizacao/app/directives/grafico-legenda/modalGraficoUfs.tmpl.html","<md-dialog id=modalGraficos style=min-width:800px><div id=su-fiscalizacao><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>{{scope.tituloGrafico}}</span><md-button class=md-icon-button ng-click=cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><md-dialog-content style=\"min-width:800px !important\"><div class=md-dialog-content><div class=md-block flex-gt-xs flex=100 class=buttons-action ng-repeat=\"grafico in scope._listaGrafico track by $index\"><div class=md-block flex=100 flex-xs=100><h2 class=quantidade-infracao style=\"margin: 0\">Estado: {{grafico.uf}}</h2></div><div class=md-block flex=100 flex-xs=100><h4 class=quantidade-infracao>Total da UF: {{grafico.total}}</h4></div><div layout=row layout-xs=column ng-if=\"scope.identificador == \'caracterizacao\' || scope.identificador ==\'motivacao\' || scope.identificador == \'natureza\'\"><div class=md-block flex=30 flex-md=50 flex-xs=100 style=\"align-self: center\"><canvas height=300px class=\"chart chart-pie\" chart-options=grafico.options chart-data=grafico.data chart-labels=grafico.labels chart-colors=grafico.colors chart-series=grafico.series></canvas></div><div class=md-block flex=10 flex-xs=100></div><div class=md-block flex=50 flex-md=50 flex-xs=100 layout=row style=\"display: flex; flex-direction: column\"><div ng-repeat=\"item in grafico.series track by $index\" style=\"margin: 20px 0px 10px 10px\"><div class=md-block flex-gt-xs flex=100><span class=nivel-legenda ng-style=\"{\'background\' : grafico.colors[$index]}\"></span> <span class=nivel-legenda-title>{{$index + 1}} - {{item}} (<strong>{{grafico.data[$index]}}</strong>)</span></div></div></div></div><div layout=row layout-xs=column ng-if=\"scope.identificador == \'Anual\' \"><div class=md-block flex=30 flex-md=50 flex-xs=100 style=\"align-self: center\"><canvas height=300px class=\"chart chart-doughnut\" chart-options=grafico.options chart-data=grafico.data chart-labels=grafico.labels chart-colors=grafico.colors chart-series=grafico.series></canvas></div><div class=md-block flex=10 flex-xs=100></div><div class=md-block flex=50 flex-md=50 flex-xs=100 layout=row style=\"display: flex; flex-direction: column\"><div ng-repeat=\"item in grafico.series track by $index\" style=\"margin: 20px 0px 10px 10px\"><div class=md-block flex-gt-xs flex=100><span class=nivel-legenda ng-style=\"{\'background\' : grafico.colors[$index]}\"></span> <span class=nivel-legenda-title>{{$index + 1}} - {{item}} (<strong>{{grafico.data[$index]}}</strong>)</span></div></div></div></div><div layout=row layout-xs=column ng-if=\"scope.identificador == \'assunto\' \"><div class=md-block flex=100 flex-xs=100 style=\"align-self: center\"><canvas height=300px width=500px class=\"chart chart-bar\" chart-options=grafico.options chart-data=grafico.data chart-labels=grafico.labels chart-colors=grafico.colors chart-series=grafico.series chart-options=options></canvas></div></div></div></div></md-dialog-content><md-dialog-actions layout=row><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=md-primary ng-click=scope.cancelModal()><md-icon md-font-set=material-icons>close</md-icon><span>Fechar</span></md-button></div></section></div></md-dialog-actions></div></md-dialog>");
$templateCache.put("scripts-fiscalizacao/pages/geral/view/breadcrumb.html","<div id=breadcrumb><ul class=breadcrumb><span class=voce-esta-aqui>VOCÊ ESTÁ AQUI:</span><li class=migalha-pao ng-repeat=\"step in steps | limitTo:(steps.length-1)\"><a ng-href={{step.ncyBreadcrumbLink}} ng-bind-html=step.ncyBreadcrumbLabel></a></li><li class=\"migalha-pao active\" ng-repeat=\"step in steps | limitTo:-1\"><span ng-bind-html=step.ncyBreadcrumbLabel></span></li></ul></div>");
$templateCache.put("scripts-fiscalizacao/pages/geral/view/header.html","<md-toolbar layout=row id=navigation-bar class=md-whiteframe-z2><div id=menu-button-container><md-button class=menu id=menu-button ng-click=ul.toggleMenu() flex aria-label=\"Menu da Aplicação\"><!--<md-icon class=\"icone60\" md-font-set=\"material-icons\" sixe=\"38\">menu</md-icon>--><ng-md-icon icon=menu size=38 style=\"fill:white; margin: 10px\"></ng-md-icon></md-button></div><div id=nome-sistema layout=row layout-align=\"space-around none\" class=elemento-barra-navegacao><!--<div class=\"titulo-app\" style=\"color:white\">--><!--<span>SPU</span>--><!--</div>--><div layout=column hide-xs=\"\" layout-align=\"center center\" class=\"hide-xs layout-align-center-center layout-column\"><a ui-sref=HOME><img src=assets/img/logo-spu.png></a></div><!--<div class=\"divisor-vertical\" hide show-gt-sm style=\"color:white\"></div>--><!--<span hide show-gt-sm class=\"subtitulo-app md-caption\" style=\"color:white\">Secretaria do Patrimônio da União</span>--><!--<div flex></div>--></div><div layout=row class=\"icones-menu layout-align-end-center layout-row flex-75\" flex=75 layout-align=\"end center\"><a ui-sref=HOME class=posicao-itens-barra-menu><a href=/fiscalizacao/login><ng-md-icon icon=home size=35 alt=Home aria-label=\"Página inicial\"><svg xmlns=http://www.w3.org/2000/svg viewBox=\"0 0 24 24\" width=35 height=35><path d=\"M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z\"></path></svg></ng-md-icon></a><ng-md-icon icon=notifications size=35 alt=Notificações aria-label=Notificações><svg xmlns=http://www.w3.org/2000/svg viewBox=\"0 0 24 24\" width=35 height=35><path d=\"M11.5 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2z\"></path><path d=\"M18 16v-5.5c0-3.07-2.13-5.64-5-6.32V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5v.68c-2.87.68-5 3.25-5 6.32V16l-2 2v1h17v-1l-2-2z\"></path></svg></ng-md-icon><ng-md-icon icon=help size=35 alt=Ajuda aria-label=Ajuda><svg xmlns=http://www.w3.org/2000/svg viewBox=\"0 0 24 24\" width=35 height=35><path d=\"M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z\"></path></svg></ng-md-icon><a href=/fiscalizacao/api/saml/logout><ng-md-icon icon=do_not_disturb size=35 alt=Notificações aria-label=Logout><svg xmlns=http://www.w3.org/2000/svg viewBox=\"0 0 24 24\" width=35 height=35><path d=\"M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z\"></path></svg></ng-md-icon></a><button class=\"md-fab md-primary md-hue-2 md-button md-ink-ripple\" type=button aria-label=Configurações><md-icon ng-bind=\"\'person\'\"></md-icon></button></a></div></md-toolbar><!--<md-toolbar layout=\"row\" class=\"md-whiteframe-z3 md-primary md-hue-2\" id=\"breadcrumb-bar\">--><!--<div layout-align=\"center center\" flex=\"100\">--><!--<bread-crumb dados=\"menuAcessos\"></bread-crumb>--><!--</div>--><!--</md-toolbar>--><md-toolbar layout=row class=\"md-whiteframe-z3 _md layout-row _md-toolbar-transitions\" style=\"min-height: 32px !important; background: #38813C !important\"><div id=breadcrumb flex=60 ncy-breadcrumb=\"\" class=\"ng-isolate-scope flex-60\"><ul class=breadcrumb><span class=voce-esta-aqui>VOCÊ ESTÁ AQUI:</span> <!-- ngRepeat: step in steps | limitTo:(steps.length-1) --><!-- ngRepeat: step in steps | limitTo:-1 --></ul></div><div flex=\"\" layout=row layout-align=\"end center\" class=\"layout-align-end-center layout-row flex\"><contador-sessao></contador-sessao></div><!-- Container Menu e Conteúdo --></md-toolbar>");
$templateCache.put("scripts-fiscalizacao/pages/geral/view/template.html","<div flex layout=column ng-controller=\"MenuController as ul\"><!-- Conteúdo Principal --><div class=nooverflow style=\"overflow:auto;height: 100%\"><div class=noprint ng-include=\"\'scripts-fiscalizacao/pages/geral/view/header.html\'\"></div><md-content flex layout=column ng-controller=\"GlobalController as vm\" style=\"min-height: calc(100% - 146px); padding: 0 !important\"><menu></menu><div ui-view></div></md-content><footer class=\"noprint footer p-b-0\">SPU - Secretaria de Patrimônio da União | Esplanada dos Ministérios - Bloco C e K - Brasília-DF - Versão: _</footer></div></div><script type=text/ng-template id=toast-template-error.html><md-toast md-theme=\"error-toast\">\n        <div class=\"toast-padding\" layout=\"column\" ng-show=\"isArray\">\n            <h3 flex>{{titulo}}</h3>\n            <div flex ng-repeat=\"msg in msgs\">\n                <span flex>{{msg}}</span>\n            </div>\n        </div>\n\n        <span flex ng-show=\"!isArray\">{{msgs}}</br></span>\n    </md-toast></script><script type=text/ng-template id=toast-template-warning.html><md-toast md-theme=\"warning-toast\">\n        <span flex>{{msgs}}</span>\n    </md-toast></script><script type=text/ng-template id=toast-template-success.html><md-toast md-theme=\"success-toast\">\n        <span flex>{{msgs}}</span>\n    </md-toast></script>");
$templateCache.put("scripts-fiscalizacao/pages/painel/modalTemplate/modalTemplate.tmpl.html","<md-dialog><form ng-cloak><div layout=row layout-xs=column style=\"padding: 20px; font-size: 16px; text-align: center\"><div class=md-block flex-gt-xs flex=100><span>Atenção</span></div></div><div layout=row layout-xs=column style=\"padding: 0 20px 20px 20px\"><div class=md-block flex-gt-xs flex=100><span>Selecione o tipo de arquivo desejado!</span></div></div><div layout=row layout-xs=column class=\"buttons-action m-t-15\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" type=button id=dwn-btn ng-click=scope.gerarPng()><span>PNG</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.downloadPDF()><span>PDF</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.cancelModal()><span>Fechar</span></md-button></div></div></div></form></md-dialog>");
$templateCache.put("scripts-fiscalizacao/app/directives/ci-mapa/view/ci-mapa-template.html","<div class=div-mapa><div layout=column class=\"mapa-controles mapa-ferramentas m-t-10\"><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=marcarPonto(); ng-if=!btMarcar><ng-md-icon icon=room style=\"fill: #fff\"><md-tooltip>Marcar Ponto</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=limpar() ng-if=!btLimpar><ng-md-icon icon=delete style=\"fill: #fff\"><md-tooltip>Apagar</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=desenharBenfeitoria() ng-if=btDesenhar><ng-md-icon icon=create style=\"fill: #fff\"><md-tooltip>Desenhar</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=geraKml() ng-if=btExportar><ng-md-icon icon=file_download style=\"fill: #fff\"><md-tooltip>Exportar KML</md-tooltip></ng-md-icon></md-button></div><div class=\"mapa-controles controles-bottom\" layout=row layout-align=\"end end\"><div class=mapa-legenda ng-show=legenda><div class=mapa-legenda-conteudo style=\"font-size: 11px\" ng-class=\"exibirLegenda ? \'mapa-legenda-conteudo-expandido\': \'\'\" layout=column><div class=legenda-icon-container><div class=\"div-cor div-azul\"></div><span class=legenda-icon-text>Sem prazo de cumprimento</span></div><div class=legenda-icon-container><div class=\"div-cor div-amarela\"></div><span class=legenda-icon-text>Prazo proximo do vencimento</span></div><div class=legenda-icon-container><div class=\"div-cor div-vermelha\"></div><span class=legenda-icon-text>Prazo cumprimento vencido</span></div><div class=legenda-icon-container><div class=\"div-cor div-verde\"></div><span class=legenda-icon-text>Executada</span></div><div class=legenda-icon-container><div class=\"div-cor div-cinza\"></div><span class=legenda-icon-text>Em execução</span></div><div class=legenda-icon-container><span class=legenda-mapa-icon><i class=material-icons>place</i> </span><span class=legenda-icon-text>Não Vinculada</span></div><div class=legenda-icon-container><span class=legenda-mapa-icon><i class=material-icons>grade</i> </span><span class=legenda-icon-text>Vinculada</span></div><div class=legenda-icon-container><span class=legenda-mapa-icon><i class=material-icons>change_history</i> </span><span class=legenda-icon-text>Fiscalização executada por agente externo</span></div></div><md-button class=mapa-legenda-botao ng-click=\"exibirLegenda = !exibirLegenda\">Legenda</md-button></div><div ng-if=showMinimapa class=mapa-minimapa ng-click=alterarBingVisible()><ci-minimapa height={{heightMinimapa}} width={{widthMinimapa}}></ci-minimapa></div></div><style>.ol-popup {\n            position: absolute;\n            background-color: white;\n            -webkit-filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2));\n            filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2));\n            padding: 15px;\n            border-radius: 10px;\n            border: 1px solid #cccccc;\n            bottom: 12px;\n            left: -50px;\n            min-width: 280px;\n        }\n\n        .ol-popup:after, .ol-popup:before {\n            top: 100%;\n            border: solid transparent;\n            content: \" \";\n            height: 0;\n            width: 0;\n            position: absolute;\n            pointer-events: none;\n        }\n\n        .ol-popup:after {\n            border-top-color: white;\n            border-width: 10px;\n            left: 48px;\n            margin-left: -10px;\n        }\n\n        .ol-popup:before {\n            border-top-color: #cccccc;\n            border-width: 11px;\n            left: 48px;\n            margin-left: -11px;\n        }</style><div id=popUpInfoDemanda class=ol-popup ng-show=!tipo><div><div layout=row layout-align=center><h3>{{demanda.id | numeroDemanda }}</h3></div><p><b>RIP: </b>{{demanda.endereco.rip}}</p><p><b>Motivação: </b>{{demanda.motivacao.nome}}</p><p><b>Assunto: </b>{{demanda.assunto.nome}}</p><p id=pJustificativa ng-mouseover=changeJustificativaOnMouseOver() ng-mouseleave=changeJustificativaOnMouseLeave() style=\"word-wrap: break-word\"><b>Justificativa: </b><span id=spanJusticativa>{{demanda.justificativa}}</span></p><p><b>Número Processo: </b>{{demanda.processoAdministrativo}}</p><p><b>Prazo: </b>{{demanda.dtPrazo | date:\'dd/MM/yyyy\'}}</p></div><div class=md-block flex-gt-xs flex=100><div layout=row><div flex=40><p ng-show=demanda.areaImovel>{{demanda.areaImovel}} m²</p></div><div flex=60><div layout=row layout-align=end><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=visualizarDemanda()><ng-md-icon icon=search style=\"fill: #fff\"><md-tooltip>Visualizar</md-tooltip></ng-md-icon></md-button><md-button class=\"mapa-botoes-icone md-icon-button\" ng-click=adicionarDemanda(demanda) ng-show=\"demanda.status !== \'VINCULADA\' && demanda.status !== \'EXECUTADA\'\"><ng-md-icon icon=add_circle style=\"fill: #fff\"><md-tooltip>Adicionar a OF</md-tooltip></ng-md-icon></md-button></div></div></div></div></div><div id=popUpDemandasOF class=ol-popup ng-show=\"demandasAdicionadas && demandasAdicionadas.length > 0\"><div layout=row layout-xs=column><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-left width=15%><span>Número da Demanda</span></th><th md-column class=text-center width=10%><span>Ações</span></th></tr></thead><tbody><tr md-row ng-repeat=\"demandas in demandasAdicionadas\" style=\"height: 63px\"><td md-cell class=text-left>{{demandas.id | numeroDemanda}}</td><td md-cell class=text-center><a ng-click=removerDemanda(demandas) class=cursor-pointer><md-tooltip md-direction=top>Remover</md-tooltip><ng-md-icon icon=delete></ng-md-icon></a></td></tr></tbody></table></md-table-container></div></div></div></div>");
$templateCache.put("scripts-fiscalizacao/app/directives/localizacao/templates/inc-informar-coordenadas-fiscalizacao.html","<ng-form name=coordenadasForm layout=column class=filtro-mapa isolate-form><div layout=row><md-input-container class=md-block flex><label>Sistema de Referência</label><md-select ng-model=sistemaReferencia name=sistemaReferencia ng-disabled=isSistemaReferenciaDisabled() ng-change=handleItemSelected(sistemaReferencia)><md-option ng-repeat=\"item in sistemasReferencia track by item.name\" ng-value=item.name>{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=column><div layout=column><p ng-class=\"!formatoCoordenadas && submittedCoodenadas ? \'input-invalid\' : null\">Formato das Coordenadas *</p><md-radio-group ng-class=\"!formatoCoordenadas && submittedCoodenadas? \'radio-invalid md-off\' : null\" ng-model=formatoCoordenadas name=formatoCoordenadas layout=row><md-radio-button class=\"md-primary radio-column\" value=GMS>Grau, minuto e segundo</md-radio-button><md-radio-button class=\"md-primary radio-column\" value=GD>Grau decimal</md-radio-button><md-radio-button class=\"md-primary radio-column\" value=M>Métricas</md-radio-button></md-radio-group></div><div layout=column ng-show=\"formatoCoordenadas === \'GMS\'\"><div layout=row class=m-t-20><div class=\"md-block radio-options\" flex-gt-xs flex=40><label><span ng-class=\"!grauMinutoSegundo.latitude.hemisferio && submittedCoodenadas ? \'input-invalid\' : null\">Latitude *</span></label><md-radio-group ng-class=\"!grauMinutoSegundo.latitude.hemisferio && submittedCoodenadas ? \'radio-invalid md-off\' : null\" ng-disabled=isGrauMinutoSegundoDisabled() ng-model=grauMinutoSegundo.latitude.hemisferio name=gmsLatHemisferio flex><md-radio-button class=\"md-primary radio-column\" value=N>Norte</md-radio-button><md-radio-button class=\"md-primary radio-column\" value=S>Sul</md-radio-button></md-radio-group></div><md-input-container ng-class=\"!grauMinutoSegundo.latitude.graus && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" flex=20><label>Graus°</label><input ng-disabled=isGrauMinutoSegundoDisabled() type=text ng-model=grauMinutoSegundo.latitude.graus name=gmsLatGraus ui-number-mask=0 ui-hide-group-sep></md-input-container><md-input-container ng-class=\"!grauMinutoSegundo.latitude.minutos && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" flex=20><label>Minutos\'</label><input ng-disabled=isGrauMinutoSegundoDisabled() type=text ng-model=grauMinutoSegundo.latitude.minutos name=gmsLatMinutos ui-number-mask=0 ui-hide-group-sep></md-input-container><md-input-container ng-class=\"!grauMinutoSegundo.latitude.segundos && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" flex=20><label>Segundos\"</label><input ng-disabled=isGrauMinutoSegundoDisabled() type=text ng-model=grauMinutoSegundo.latitude.segundos name=gmsLatSegundos ui-number-mask=2 ui-hide-group-sep></md-input-container></div><div layout=row><div class=\"md-block radio-options\" flex-gt-xs flex=40><label><span ng-class=\"!grauMinutoSegundo.longitude.hemisferio && submittedCoodenadas ? \'input-invalid\' : null\">Longitude *</span></label><md-radio-group ng-class=\"!grauMinutoSegundo.longitude.hemisferio && submittedCoodenadas ? \'radio-invalid md-off\' : null\" ng-disabled=isGrauMinutoSegundoDisabled() ng-model=grauMinutoSegundo.longitude.hemisferio name=gmsLongHemisferio flex><md-radio-button class=\"md-primary radio-column\" value=O>Oeste</md-radio-button><md-radio-button class=\"md-primary radio-column\" value=L>Leste</md-radio-button></md-radio-group></div><md-input-container ng-class=\"!grauMinutoSegundo.longitude.graus && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" flex=20><label>Graus°</label><input ng-disabled=isGrauMinutoSegundoDisabled() type=text ng-model=grauMinutoSegundo.longitude.graus name=gmsLongGraus ui-number-mask=0 ui-hide-group-sep></md-input-container><md-input-container ng-class=\"!grauMinutoSegundo.longitude.minutos && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" flex=20><label>Minutos\'</label><input ng-disabled=isGrauMinutoSegundoDisabled() type=text ng-model=grauMinutoSegundo.longitude.minutos name=gmsLongMinutos ui-number-mask=0 ui-hide-group-sep></md-input-container><md-input-container ng-class=\"!grauMinutoSegundo.longitude.segundos && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" flex=20><label>Segundos\"</label><input ng-disabled=isGrauMinutoSegundoDisabled() type=text ng-model=grauMinutoSegundo.longitude.segundos name=gmsLongSegundos ui-number-mask=2 ui-hide-group-sep></md-input-container></div></div><div layout=column ng-show=\"formatoCoordenadas === \'GD\'\"><div layout=row class=m-t-20><div class=\"md-block radio-options\" flex-gt-xs flex=40><label><span ng-class=\"!grauDecimal.latitude.hemisferio && submittedCoodenadas ? \'input-invalid\' : null\">Latitude *</span></label><md-radio-group ng-disabled=isGrauDecimalDisabled() ng-model=grauDecimal.latitude.hemisferio name=gdLatHemisferio ng-change=onChangeHemisferioLatitude() flex><md-radio-button class=\"md-primary radio-column\" value=N>Norte</md-radio-button><md-radio-button class=\"md-primary radio-column\" value=S>Sul</md-radio-button></md-radio-group></div><md-input-container ng-class=\"!grauDecimal.latitude.decimal && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" class=md-block flex=60><label>Decimal</label><input ng-disabled=isGrauDecimalDisabled() type=text ng-model=grauDecimal.latitude.decimal name=gdLatDecimal ui-number-mask=8 ui-hide-group-sep ui-negative-number></md-input-container></div><div layout=row><div class=\"md-block radio-options\" flex-gt-xs flex=40><label><span ng-class=\"!grauDecimal.latitude.hemisferio && submittedCoodenadas ? \'input-invalid\' : null\">Longitude *</span></label><md-radio-group ng-disabled=isGrauDecimalDisabled() ng-model=grauDecimal.longitude.hemisferio name=gdLongHemisferio ng-change=onChangeHemisferioLongitude() flex><md-radio-button class=\"md-primary radio-column\" value=O>Oeste</md-radio-button><md-radio-button class=\"md-primary radio-column\" value=L>Leste</md-radio-button></md-radio-group></div><md-input-container ng-class=\"!grauDecimal.longitude.decimal && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" class=md-block flex=60><label>Decimal</label><input ng-disabled=isGrauDecimalDisabled() type=text ng-model=grauDecimal.longitude.decimal name=gdLongDecimal ui-number-mask=8 ui-hide-group-sep ui-negative-number></md-input-container></div></div><div layout=column ng-show=\"formatoCoordenadas === \'M\'\"><div layout=row class=m-t-20><md-input-container ng-class=\"!metricas.coordenadaE && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" flex><label>Coordenada E</label><input ng-disabled=isMetricasDisabled() type=text ng-model=metricas.coordenadaE name=metricasCoordenadaE ui-number-mask=4 ui-hide-group-sep></md-input-container><md-input-container ng-class=\"!metricas.coordenadaN && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" flex><label>Coordenada N</label><input ng-disabled=isMetricasDisabled() type=text ng-model=metricas.coordenadaN name=metricasCoordenadaN ui-number-mask=4 ui-hide-group-sep></md-input-container><md-input-container ng-class=\"!metricas.fusoUTM && submittedCoodenadas ? \'md-input-invalid\' : \'\'\" flex><label>Fuso UTM</label><md-select ng-disabled=isMetricasDisabled() ng-model=metricas.fusoUTM name=metricasFusoUTM ng-model-options=\"{ trackBy: \'$value.zona + $value.hemisferio\'}\"><md-option ng-repeat=\"item in fusosUTM\" ng-value=item>{{item.zona + \" \" + item.hemisferio}}</md-option></md-select></md-input-container></div></div><div layout=row layout-align=\"end center\"><md-button ng-show=!esconderBotaoMarcarPonto() class=\"md-primary md-raised primeira-maiuscula\" ng-click=marcarPonto()>Marcar Ponto</md-button></div></div></ng-form>");
$templateCache.put("scripts-fiscalizacao/app/directives/localizacao/templates/inc-localizacao-imovel.html","<div layout=column><inc-mensagem tipo=alerta no-icon>Nome do Cadastrador: {{usuario.nome}} - {{usuario.cpf}}<br>UG Executora do Titular: {{usuario.idOrganizacao}} - {{usuario.organizacao}}<br>Você pode cadastrar imóveis para: {{getProprietariosCadastro()}}<br>Locais permitidos: {{usuario.jurisdicoes.sort().toString()}}</inc-mensagem><md-input-container name=tipoImovelContainer class=md-block flex-gt-sm><label>Tipo de Imóvel</label><md-select campo-ajuda=INC_FORM_LOC_TIPO_IMOVEL required ng-model=imovel.tipoImovel ng-model-options=\"{trackBy: \'$value.id\'}\" name=tipoImovel ng-class=\"isDadoAlterado(\'imovel.tipoImovel.id\')\" ng-change=atualizaTipoImovel() ng-disabled=isTipoImovelDisabled()><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposImoveis | filter : isNotTipoUnidadeAutonomaPrimeiraParte\" ng-value=item>{{item.descricao}}</md-option><md-optgroup label=\"Unidade Autônoma\"><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposImoveis | filter : isTipoUnidadeAutonoma\" ng-value=item>{{item.descricao}}</md-option></md-optgroup><md-option campo-ajuda={{item.codigoSistemico}} ng-repeat=\"item in tiposImoveis | filter : isNotTipoUnidadeAutonomaSegundaParte\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><v-accordion multiple class=vAccordion--default><v-pane expanded><v-pane-header>Dados de Localização do Imóvel</v-pane-header><v-pane-content><div layout=column><div layout-gt-xs=row><md-input-container class=\"md-block margem-direita-padrao\" flex-gt-sm><label>País</label><md-select campo-ajuda=INC_FORM_LOC_PAIS ng-required=!isImovelEspelhoDagua() ng-model=imovel.enderecamento.pais ng-model-options=\"{trackBy: \'$value.id\'}\" name=pais ng-change=buscaCidadesPorPais() ng-disabled=isPaisDisabled() ng-class=\"isDadoAlterado(\'imovel.enderecamento.pais.id\')\"><md-option ng-repeat=\"item in paises\" ng-value=item>{{item.descricao}}</md-option></md-select></md-input-container><div layout=row flex-gt-xs ng-show=isPaisBrasil();><md-input-container class=\"md-block padding-padrao-interface\" flex><label>CEP</label><input campo-ajuda=INC_FORM_LOC_CEP campo-ajuda=INC_FORM_LOC_CEP ui-br-cep-mask ng-model=imovel.enderecamento.cep ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-change=buscaEnderecoPorCep() name=cep ng-class=\"isDadoAlterado(\'imovel.enderecamento.cep\')\" ng-disabled=isCepDisabled()></md-input-container><div style=\"margin: 33px 5px\"><a href=http://www.buscacep.correios.com.br/sistemas/buscacep/ target=_blank>Não sei o CEP</a></div></div></div><inc-mensagem titulo=\"Ponto do Imóvel Obrigatório\" tipo=erro>Para o cadastro do imóvel é necessário marcar um ponto na testada principal do imóvel. Clique no local desejado no mapa abaixo para marcar a localização.</inc-mensagem><v-accordion class=vAccordion--default><v-pane expanded><v-pane-header>Informar coordenadas para o ponto</v-pane-header><v-pane-content><inc-informar-coordenadas imovel=imovel action={{action}}></inc-informar-coordenadas></v-pane-content></v-pane></v-accordion><div layout=row class=mapaLocalizacao><ci-mapa showminimapa=true heightminimapa=100 widthminimapa=100 tabindex=-1 imovel=imovel action={{action}} habilitar-desenho=false edificacoes=edificacoesUnidadeAutonoma></ci-mapa></div><div layout-gt-xs=row class=tamanho-container ng-show=isPaisBrasil()><md-input-container class=\"md-block padding-padrao-interface margem-baixa-padrao\" flex-gt-sm flex=33><label>UF</label><input campo-ajuda=INC_FORM_LOC_UF ng-model=imovel.enderecamento.uf ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-disabled=true ng-class=\"isDadoAlterado(\'imovel.enderecamento.uf\')\"></md-input-container><md-input-container class=\"md-block padding-padrao-interface margem-baixa-padrao\" flex=33 flex-gt-xs><input campo-ajuda=INC_FORM_LOC_MUNICIPIO ng-model=imovel.enderecamento.municipio ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-disabled=true placeholder=Município ng-class=\"isDadoAlterado(\'imovel.enderecamento.municipio\')\"></md-input-container><md-input-container class=md-block flex=33 flex-gt-sm><label>Tipo de Logradouro</label><md-select campo-ajuda=INC_FORM_LOC_TIPO_LOGRADOURO ng-model=imovel.enderecamento.tipoLogradouro ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-disabled=isTipoLogradouroDisabled() ng-class=\"isDadoAlterado(\'imovel.enderecamento.tipoLogradouro\')\"><md-option ng-repeat=\"logradouro in tiposLogradouro\" value={{logradouro.descricao}}>{{logradouro.descricao}}</md-option></md-select></md-input-container></div><div layout-gt-sm=row class=tamanho-container ng-show=isPaisBrasil()><md-input-container class=\"md-block padding-padrao-interface margem-top-padrao margem-baixa-padrao\" flex=33 flex-gt-xs><label>Logradouro</label><input campo-ajuda=INC_FORM_LOC_LOGRADOURO ng-model=imovel.enderecamento.logradouro ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" ng-disabled=isLogradouroDisabled() ng-class=\"isDadoAlterado(\'imovel.enderecamento.logradouro\')\"></md-input-container><md-input-container class=\"md-block padding-padrao-interface margem-top-padrao margem-baixa-padrao\" flex=33 flex-gt-sm><input campo-ajuda=INC_FORM_LOC_NUMERO ng-model=imovel.numero name=numero ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" placeholder=Número ng-class=\"isDadoAlterado(\'imovel.numero\')\" ng-disabled=isNumeroDisabled()></md-input-container><md-input-container class=md-block flex=33 flex-gt-xs><label>Bairro/Distrito</label><input campo-ajuda=INC_FORM_LOC_BAIRRO ng-model=imovel.enderecamento.bairro ng-required=\"isPaisBrasil() && !isImovelEspelhoDagua()\" name=bairro ng-disabled=isBairroDisabled() ng-class=\"isDadoAlterado(\'imovel.enderecamento.bairro\')\"></md-input-container></div><div layout-gt-sm=row class=tamanho-container ng-show=!isPaisBrasil()><md-input-container class=md-block flex-gt-sm ng-show=\"cidadesExteriores.length > 0\"><label>Cidade Exterior</label><md-select campo-ajuda=INC_FORM_LOC_CIDADE_EXTERIOR ng-model=cidadeExterior name=cidadeExterior ng-required=\"!isPaisBrasil() && !isImovelEspelhoDagua() && cidadesExteriores.length > 0\" ng-change=atualizaCidadeExterior() ng-disabled=isCidadeExteriorDisabled() ng-class=\"isDadoAlterado(\'imovel.enderecamento.cidadeExterior\')\"><md-option ng-repeat=\"cidade in cidadesExteriores\" value={{cidade}}>{{cidade}}</md-option><md-option value=Outro>Outro</md-option></md-select></md-input-container><md-input-container class=\"md-block padding-padrao-interface margem-top-padrao margem-baixa-padrao\" flex-gt-sm ng-show=\"cidadeExterior == \'Outro\' || cidadesExteriores.length == 0\"><label>Cidade Exterior</label><input campo-ajuda=INC_FORM_LOC_CIDADE_EXTERIOR name=cidadeExteriorText ng-model=imovel.enderecamento.cidadeExterior ng-required=\"!isPaisBrasil() && !isImovelEspelhoDagua() && (imovel.enderecamento.cidadeExterior == \'Outro\' || cidadesExteriores.length == 0)\" ng-disabled=isCidadeExteriorDisabled() ng-class=\"isDadoAlterado(\'imovel.enderecamento.cidadeExterior\')\"></md-input-container><md-input-container class=\"md-block margem-top-padrao margem-baixa-padrao\" flex-gt-xs><label>Código Postal</label><input campo-ajuda=INC_FORM_LOC_CODIGO_POSTAL ng-model=imovel.enderecamento.codigoPostal ng-required=\"!isPaisBrasil() && !isImovelEspelhoDagua()\" ng-class=\"isDadoAlterado(\'imovel.enderecamento.codigoPostal\')\" ng-disabled=isCodigoPostalDisabled()></md-input-container></div><div class=ultimo-container><md-input-container class=md-block ng-show=isPaisBrasil()><label>Complemento</label><textarea campo-ajuda=INC_FORM_LOC_COMPLEMENTO ng-model=imovel.complemento md-maxlength=500 ng-class=\"isDadoAlterado(\'imovel.complemento\')\" ng-disabled=isComplementoDisabled()></textarea></md-input-container><md-input-container class=\"md-block margem-top-padrao margem-baixa-padrao\" ng-show=!isPaisBrasil()><label>Endereço</label><textarea campo-ajuda=INC_FORM_LOC_ENDERECO ng-model=imovel.enderecamento.endereco md-maxlength=500 ng-class=\"isDadoAlterado(\'imovel.enderecamento.endereco\')\" ng-disabled=isEnderecoDisabled()></textarea></md-input-container></div></div></v-pane-content></v-pane></v-accordion></div>");
$templateCache.put("scripts-fiscalizacao/pages/demanda/detalhar/views/demanda-detalhar.tmpl.html","<md-tabs md-dynamic-height md-border-bottom><md-tab label=\"Cadastrar Demanda\"><form ng-cloak name=formRegistro ng-submit=scope.persistir(scope.registro) novalidate><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=66><div layout=row layout-xs=column><div flex><h2 ng-show=!scope.registro.id>Cadastrar demanda</h2><h2 ng-show=\"scope.registro.id && !scope.apenasLeitura\">Editar demanda - {{scope.registro.id | numeroDemanda}}</h2><h2 ng-show=\"scope.registro.id && scope.apenasLeitura\">Visualizar demanda - {{scope.registro.id | numeroDemanda}}</h2></div></div><h3>Dados Básicos da Demanda de Fiscalização</h3><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><label><span>Motivação</span></label><md-select ng-model=scope.registro.motivacao ng-disabled=scope.apenasLeitura required><md-option ng-value=item ng-repeat=\"item in scope._motivacoes\">{{ item.nome }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column class=m-b-20><div class=md-block flex-gt-xs flex=100><label style=\"padding-right: 20px\"><span>Esta demanda faz parte do PAF?</span></label><md-radio-group ng-model=scope.paf ng-change=scope.changePaf() ng-disabled=scope.apenasLeitura required><md-radio-button ng-value=true class=\"md-primary radio-column\">Sim</md-radio-button><md-radio-button ng-value=false class=\"md-primary radio-column\">Não</md-radio-button></md-radio-group></div></div><div layout=row layout-xs=column ng-show=scope.paf><md-input-container class=md-block flex-gt-xs flex=100><label><span>Assunto</span></label><md-select ng-model=scope.registro.assunto ng-disabled=scope.apenasLeitura ng-change=scope.changeAssunto(scope.registro.assunto) ng-required=\"scope.paf == true\"><md-option ng-value=item ng-repeat=\"item in scope._assuntos\">{{ item.nome }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.registro.assunto.tipo == \'OUTROS\'\"><md-input-container class=md-block flex-gt-xs flex=100><input ng-model=scope.registro.outros type=text maxlength=100 ng-disabled=scope.apenasLeitura ng-required=\"scope.registro.assunto.tipo == \'OUTROS\'\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Destinação</span></label><md-select ng-model=scope.registro.tipoImovel ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._tiposImovel\">{{ item.nome }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block ng-if=!scope.processo flex-gt-xs flex=33><label><span>Processo Administrativo</span></label><input ng-model=scope.registro.processoAdministrativo type=text ui-mask=99999.999999/9999-99 ng-disabled=\"scope.apenasLeitura || scope.processo\" ng-required=!scope.processo></md-input-container><md-input-container class=md-block ng-if=scope.processo style=\"opacity: 0.6\" flex-gt-xs flex=33><label><span>Processo Administrativo</span></label><input type=text ng-disabled=true></md-input-container><md-checkbox class=\"m-b-30 md-primary\" ng-model=scope.processo ng-change=scope.changeProcesso() style=\"align-self: center\" ng-disabled=scope.apenasLeitura flex-gt-xs flex=67><span>Não existe processo administrativo criado para esta demanda</span></md-checkbox></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa da demanda</label><textarea ng-model=scope.registro.justificativa maxlength=3000 md-maxlength=3000 rows=3 md-select-on-focus ng-disabled=scope.apenasLeitura required></textarea></md-input-container></div><div layout=row layout-xs=column ng-if=scope.showJustificativaDemandaOf><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa da nova demanda</label><textarea ng-model=scope.registro.justificativaDemandaOf maxlength=3000 md-maxlength=3000 rows=3 md-select-on-focus ng-disabled=scope.apenasLeitura required></textarea></md-input-container></div><div layout=row layout-xs=column><div class=md-block style=\"margin-top: 9px\" flex-gt-xs flex=70><label><span>Esta demanda tem prazo legal para cumprimento?</span></label><md-radio-group ng-model=scope.prazo ng-change=scope.changePrazo() ng-disabled=scope.apenasLeitura ng-required=true><md-radio-button ng-value=true class=\"md-primary radio-column m-l-10\" style=\"min-width: auto\">Sim</md-radio-button><md-radio-button ng-value=false class=\"md-primary radio-column m-l-10\" style=\"min-width: auto\">Não</md-radio-button></md-radio-group></div><div class=md-block flex-gt-xs flex=30 ng-show=scope.prazo><md-datepicker-custom name=dtPrazo ng-model=scope.registro.dtPrazo md-custom-required=false md-custom-mask=39/19/9999 md-custom-clean-mask=false md-custom-restrict=reject ng-disabled=scope.apenasLeitura ng-required=\"scope.prazo == true\"></md-datepicker-custom><div class=validation-messages style=\"color:red; font-size: 12px\"><div ng-show=formRegistro.dtInicio.$error.valida>Escolha uma data válida!</div></div></div></div></div><div class=md-block flex-gt-xs flex=33></div></div><h3 class=m-t-20>Localização</h3><div layout=row layout-xs=column><md-input-container class=\"md-block input-group\" flex-gt-xs flex=10><label><span>RIP</span></label><input ng-model=scope.registro.endereco.rip integer-number type=text maxlength=8 ng-disabled=\"scope.apenasLeitura || scope.ripPesquisado\" ng-required=!scope.ripPesquisado ng-blur=scope.limpaRip(scope.registro.endereco.rip)></md-input-container><md-button class=\"md-primary md-raised btn-form-action button-group\" style=\"margin-bottom: 10px\" ng-disabled=\"scope.registro.endereco.rip.length !== 8\" ng-click=scope.getRIP(scope.registro.endereco.rip) ng-show=!scope.apenasLeitura><md-icon md-font-set=material-icons>search</md-icon></md-button><md-checkbox class=\"m-b-30 md-primary\" ng-change=scope.dadosEnderecoValida() ng-model=scope.ripPesquisado style=\"align-self: center\" ng-disabled=scope.apenasLeitura><span>Não possui RIP</span></md-checkbox></div><div layout=row layout-xs=column ng-show=scope.dadosEndereco><div class=md-block flex-gt-xs flex=66><v-accordion class=vAccordion--default><v-pane expanded><v-pane-header>Informar coordenadas para o ponto</v-pane-header><v-pane-content><inc-informar-coordenadas latitude=scope.registro.endereco.latitude longitude=scope.registro.endereco.longitude codigo-sistema-referencia=scope.registro.endereco.codigoSistemaReferencia sistema-referencia=scope.registro.endereco.sistemaReferencia formato-coordenadas=scope.registro.endereco.formatoCoordenadas apenas-leitura=scope.apenasLeitura></inc-informar-coordenadas></v-pane-content></v-pane></v-accordion></div></div><div layout=row layout-xs=column ng-show=scope.dadosEndereco><div class=\"md-block mapaLocalizacao\" flex-gt-xs flex=33 ng-if=\"scope.carregarMapa && scope.dadosEndereco\"><ci-mapafiscalizacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=-1 latitude=scope.registro.endereco.latitude longitude=scope.registro.endereco.longitude exportar-mapa=scope.exportarKml(kml) validar=scope.validaPonto(imovel) action=\"{{scope.apenasLeitura ? \'detail\' : \'add\'}}\" bt-exportar=true bt-desenhar=false bt-limpar=true bt-marcar=scope.apenasLeitura visualizar=false bt-exportar-png=true apenas-leitura=scope.apenasLeitura></ci-mapafiscalizacao></div><div class=md-block flex-gt-xs flex=66 style=\"margin-top: 109px; margin-left: 20px\"><div layout=row layout-xs=column><md-input-container class=\"md-block p-r-10\" flex-gt-xs flex=50><label><span>UF</span></label><md-select ng-model=scope.registro.endereco.estado ng-disabled=\"(scope.apenasLeitura || !scope.ripPesquisado)\" ng-change=\"scope.getMunicipios(scope.registro.endereco.estado, scope.validaEstadoSelecionado())\" required><md-input-container class=\"md-block p-r-10\" flex-gt-xs flex=50><md-select-header><input ng-model=pesquisarUf ng-click=$event.stopPropagation() ng-keydown=$event.stopPropagation() placeholder=\"Digite a UF...\" type=search></md-select-header></md-input-container><md-option ng-value=item ng-repeat=\"item in scope._usuario.jurisdicoes | filter: pesquisarUf | orderBy\">{{ item }}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=50 ng-show=\"scope.registro.endereco.estado != \'SP\' && scope.registro.endereco.estado != \'PI\'\"></md-input-container><div class=\"md-block p-t-10\" flex-gt-xs flex=50 ng-show=\"scope.registro.endereco.estado == \'SP\' || scope.registro.endereco.estado == \'PI\'\"><label><span>Demanda EDESC</span></label><md-radio-group ng-model=scope.registro.isEdesc ng-change=scope.getEdesc() ng-disabled=scope.apenasLeitura required><md-radio-button ng-value=true class=\"md-primary radio-column m-l-15\">Sim</md-radio-button><md-radio-button ng-value=false class=\"md-primary radio-column\">Não</md-radio-button></md-radio-group></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Área do Imóvel(m²)</span></label><input ng-model=scope.registro.areaImovel type=text maxlength=20 ui-number-mask ng-disabled=\"scope.apenasLeitura  || !scope.ripPesquisado\"></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label><span>CEP</span></label><input ng-model=scope.registro.endereco.cep ui-br-cep-mask type=text ng-change=scope.getCEP(scope.registro.endereco.cep) ng-disabled=\"scope.apenasLeitura || !scope.ripPesquisado\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Município</span></label><md-select ng-model=scope.registro.endereco.municipio ng-change=\"scope.onChangeMunicipio(scope.registro.endereco.estado, scope.registro.endereco.municipio)\" ng-disabled=\"(scope.apenasLeitura || !scope.ripPesquisado) || scope.registro.endereco.estado == null \" required><md-input-container class=\"md-block p-r-10\" flex-gt-xs flex=50><md-select-header><input ng-model=pesquisarMunicipio ng-click=$event.stopPropagation() ng-keydown=$event.stopPropagation() placeholder=\"Digite o Município...\" type=search></md-select-header></md-input-container><md-option ng-value=item ng-repeat=\"item in scope._municipios |filter: pesquisarMunicipio | orderbyAccentsName\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label><span>Bairro</span></label><input ng-model=scope.registro.endereco.bairro type=text maxlength=50 ng-disabled=\"scope.apenasLeitura  || !scope.ripPesquisado\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Tipo de logradouro</span></label><md-select ng-model=scope.registro.endereco.tipoLogradouro ng-disabled=\"scope.apenasLeitura || !scope.ripPesquisado \"><md-optgroup labe=scope.tipoLogradouro><md-option ng-value=k ng-repeat=\"(k,y) in scope._tipoLogradouro\">{{y}}</md-option></md-optgroup></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label><span>Logradouro</span></label><input ng-model=scope.registro.endereco.logradouro type=text maxlength=100 ng-disabled=\"scope.apenasLeitura  || !scope.ripPesquisado\" required></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número</span></label><input ng-model=scope.registro.endereco.numero type=text maxlength=100 ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label><span>Complemento</span></label><input ng-model=scope.registro.endereco.complemento type=text maxlength=200 ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Natureza do Imóvel</span></label><md-select ng-model=scope.registro.endereco.zoneamento ng-disabled=\"scope.apenasLeitura || scope.pontoValidado\"><md-option ng-value=item ng-repeat=\"item in scope._zoneamentos\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=50></md-input-container></div></div></div><h3 class=m-t-20>Dados do Responsável do Imóvel</h3><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=33><label><span>CPF/CNPJ do Responsável</span></label><input ng-model=scope.registro.responsavel.cpfCnpj ng-blur=validarCpf(scope.registro.responsavel.cpfCnpj) type=text ui-br-cpfcnpj-mask ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=md-block flex-gt-xs flex=66><label><span>Nome do Responsável</span></label><input ng-model=scope.registro.responsavel.nome type=text maxlength=80 ng-disabled=\"scope.apenasLeitura || scope.nomeAutomatico\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><md-select ng-model=scope.registro.edesc ng-disabled=scope.apenasLeitura ng-show=scope.registro.isEdesc ng-required=scope.registro.isEdesc><md-option ng-value=item ng-repeat=\"item in scope._nucleoEdesc\">{{ item.nucleo }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" ui-sref=fiscalizacao.demanda.listar><span>Cancelar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.restaurar(scope.registro) ng-show=\"scope.apenasLeitura && scope.registro.status == \'SUSPENSA\'\" ng-disabled=scope.buscarCEP><span>Restaurar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.suspender(scope.registro) ng-show=\"scope.apenasLeitura && scope.registro.status == \'DISPONIVEL\'\" ng-disabled=scope.buscarCEP><span>Suspender</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.persistir(scope.registro) ng-show=!scope.apenasLeitura ng-disabled=scope.buscarCEP><span>Salvar</span></md-button></div></div></div></form></md-tab><md-tab label=Histórico ng-click=scope.getHistorico() ng-disabled=!scope.registro.id><h3 class=\"m-b-20 m-t-20\">Histórico</h3><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-center width=10%><span>Data e Hora</span></th><th md-column width=45%><span>Descrição da Ocorrência</span></th><th md-column width=40%><span>Responsável</span></th><th md-column class=text-center width=5%><span>Descrição</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._historico\"><td md-cell class=text-center>{{item.dtInclusao | date : \'dd/MM/yyyy - HH:mm\'}}</td><td md-cell><span ng-show=\"item.status.tipo != \'OCORRENCIA_AUTOMATICA\'\">{{item.status.nome}}</span> <a ng-click=\"scope.verificaOcorrenciaHistorico(item.ocorrencia, scope.demandaId)\" ng-show=\"item.status.tipo == \'OCORRENCIA_AUTOMATICA\'\" style=\"cursor: pointer\">{{item.ocorrencia}}</a></td><td md-cell>{{item.nome}} {{item.cpf | brCpf}}</td><td md-cell class=text-center><a class=cursor-pointer ng-click=\"scope.showModalDetalheHistorico($event, item)\" ng-show=\"item.status.tipo != \'EXCLUSAO\' && item.detalhes.length > 0\"><md-tooltip md-direction=top>Visualizar</md-tooltip><ng-md-icon icon=search></ng-md-icon></a><a class=cursor-pointer ng-click=\"scope.showModalHistoricoJustificativaDemanda($event, item)\" ng-show=\"item.justificativaDemanda.length > 0\"><md-tooltip md-direction=top>Visualizar</md-tooltip><ng-md-icon icon=search></ng-md-icon></a></td></tr></tbody></table></md-table-container></md-tab></md-tabs>");
$templateCache.put("scripts-fiscalizacao/pages/demanda/detalhar/views/demanda-historico-justificativa-modal.tmpl.html","<md-dialog id=modal><div id=su-fiscalizacao><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Detalhe Histórico</span><md-button class=md-icon-button ng-click=scope.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div class=sgr-container-table layout=column><md-table-container><table md-table><thead md-head><tr md-row><th md-column width=100%><span>Justificativa</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._justificativaDemanda\"><td md-cell><p>Tendo em vista a não execução da demanda ao relatório nº {{scope.relatorioFiscalizacao.id}} inserida na Ordem de Fiscalização nº {{scope.relatorioFiscalizacao.ordemFiscalizacao.protocolo}}</p><p>Justificativa: {{item.justificativa}}</p><p>Nome: {{scope._historicoJustificativa.nome}}</p><p>CPF: {{scope._historicoJustificativa.cpf | brCpf}}</p><p>Emissão: {{scope._historicoJustificativa.dtInclusao | date : \'dd/MM/yyyy - HH:mm\'}}</p></td></tr></tbody></table></md-table-container></div></div></md-dialog-content><md-dialog-actions layout=row><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=md-primary ng-click=scope.cancelModal()><md-icon md-font-set=material-icons>close</md-icon><span>Fechar</span></md-button></div></section></div></md-dialog-actions></div></md-dialog>");
$templateCache.put("scripts-fiscalizacao/pages/demanda/detalhar/views/demanda-historico-modal.tmpl.html","<md-dialog id=modal><div id=su-fiscalizacao><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Detalhe Histórico</span><md-button class=md-icon-button ng-click=scope.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div class=sgr-container-table layout=column><md-table-container><table md-table><thead md-head><tr md-row><th md-column width=20%><span>Campo</span></th><th md-column width=40%><span>Atual</span></th><th md-column width=40%><span>Anterior</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._detalhes\"><td md-cell>{{item.campo}}</td><td md-cell ng-if=\"item.campo == \'CEP\'\">{{item.atual | brCep }}</td><td md-cell ng-if=\"item.campo == \'CPF/CNPJ do Responsável\'\">{{item.atual | brCpfCnpj }}</td><td md-cell ng-if=\"item.campo == \'Processo Administrativo\'\">{{item.atual | numberProcesso }}</td><td md-cell ng-if=\"item.campo != \'CEP\' && item.campo != \'Processo Administrativo\' && item.campo != \'CPF/CNPJ do Responsável\' \">{{item.atual}}</td><td md-cell ng-if=\"item.campo == \'CEP\'\">{{item.anterior | brCep }}</td><td md-cell ng-if=\"item.campo == \'CPF/CNPJ do Responsável\'\">{{item.anterior | brCpfCnpj }}</td><td md-cell ng-if=\"item.campo == \'Processo Administrativo\'\">{{item.anterior | numberProcesso }}</td><td md-cell ng-if=\"item.campo != \'CEP\' && item.campo != \'Processo Administrativo\' && item.campo != \'CPF/CNPJ do Responsável\' \">{{item.anterior}}</td></tr></tbody></table></md-table-container></div></div></md-dialog-content><md-dialog-actions layout=row><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=md-primary ng-click=scope.cancelModal()><md-icon md-font-set=material-icons>close</md-icon><span>Fechar</span></md-button></div></section></div></md-dialog-actions></div></md-dialog>");
$templateCache.put("scripts-fiscalizacao/pages/demanda/listar/view/demanda-listar.tmpl.html","<form name=formRegistro novalidate><div layout=row layout-xs=column><div flex><h2>Consulta de Demandas</h2></div></div><div layout=row layout-xs=column><div flex><h3>Filtros de Pesquisa</h3></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>Período</span></span></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número da Demanda</span></label><input integer-number ng-model=scope.filtros.id type=text></md-input-container><md-datepicker-custom name=dtInicio ng-model=scope.filtros.dtInicio md-max-date=scope.filtros.dtFim class=md-block flex-gt-xs flex=25 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true></md-datepicker-custom><md-datepicker-custom name=dtFim ng-model=scope.filtros.dtFim md-min-date=scope.filtros.dtInicio class=md-block flex-gt-xs flex=25 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true></md-datepicker-custom></div><div layout=row layout-xs=column><div class=\"md-block validation-messages\" flex-gt-xs flex=50></div><div class=\"md-block validation-messages\" flex-gt-xs flex=50 style=\"color:red; font-size: 12px\"><div ng-show=formRegistro.dtInicio.$error.maxdate>Escolha uma data menor que a data fim</div><div ng-show=formRegistro.dtFim.$error.mindate>Escolha uma data maior que a data de inicio</div></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Motivação</span></label><md-select ng-model=scope.filtros.motivacao><md-option ng-value=item ng-repeat=\"item in scope._motivacoes\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label><span>Assunto</span></label><md-select ng-model=scope.filtros.assunto><md-option ng-value=item ng-repeat=\"item in scope._assuntos\">{{ item.nome }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>UF</span></label><md-autocomplete md-selected-item=scope.filtros.endereco.estado md-search-text=scope.filtroEstado md-selected-item-change=scope.getMunicipios(scope.filtros.endereco.estado) md-items=\"item in scope._estados |filter: scope.filtroEstado | orderBy\" md-item-text=item md-dropdown-position=bottom md-min-length=0 md-max-lenght=3 md-escape-options=clear placeholder=\"Digite a UF...\"><md-item-template><span md-highlight-text=scope.filtroEstado md-highlight-flags=^i>{{item}}</span></md-item-template><md-not-found>Nenhuma UF Encontrada...</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label><span>Município</span></label><md-autocomplete md-no-cache=noCache ng-disabled=!scope.filtros.endereco.estado md-selected-item=scope.filtros.endereco.municipio md-search-text=scope.filtroMunicipio md-items=\"item in scope._municipios |filter: scope.filtroMunicipio\" md-item-text=item.nome md-dropdown-position=bottom md-min-length=0 md-max-lenght=3 md-escape-options=clear placeholder=\"Digite o município...\"><md-item-template><span md-highlight-text=scope.filtroMunicipio md-highlight-flags=^i>{{item.nome}}</span></md-item-template><md-not-found>Nenhum Município Encontrado...</md-not-found></md-autocomplete></md-input-container></div><div layout=row layout-xs=column class=m-b-10><md-input-container class=md-block flex-gt-xs flex=50><label><span>Palavra-chave</span></label><input ng-model=scope.filtros.filtro type=text></md-input-container><md-input-container class=\"md-block m-l-20\" flex-gt-xs flex=50><label><span>Status</span></label><md-select ng-model=scope.filtros.status><md-optgroup><md-option ng-value=k ng-repeat=\"(k,v) in scope._status track by $index\">{{v}}</md-option></md-optgroup></md-select></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=50><md-button class=\"md-primary md-raised\" ui-sref=fiscalizacao.demanda.incluir><md-icon md-font-set=material-icons>add</md-icon><span>Nova Demanda</span></md-button></div><div class=md-block flex-gt-xs flex=50><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=scope.limparFiltros()><md-icon md-font-set=material-icons>filter_list</md-icon><span>Limpar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.filtrar()><md-icon md-font-set=material-icons>search</md-icon><span>Pesquisar</span></md-button></div></div></div></form><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head md-order=scope.filtros.order md-on-reorder=scope.filtrar><tr md-row><th md-column md-order-by=id class=text-right width=10%><span>Número da Demanda</span></th><th md-column md-order-by=dtInclusao width=8%><span>Última atualização</span></th><th md-column md-order-by=dtPrazo class=text-center width=10%><span>Prazo de atendimento</span></th><th md-column md-order-by=motivacaoNome><span>Motivação</span></th><th md-column md-order-by=assuntoNome><span>Assunto</span></th><th md-column md-order-by=endereco.estado width=10%><span>UF</span></th><th md-column md-order-by=endereco.nomeMunicipio width=8%><span>Município</span></th><th md-column md-order-by=status width=8%><span>Status</span></th><th md-column width=10% ng-if=scope.ocultaOf><span>OF</span></th><th md-column class=text-center width=100px><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._registros\" md-tooltip title=\"{{item.justificativa | stringLimit}}\"><td md-cell class=text-center>{{item.id | numeroDemanda}}</td><td md-cell>{{item.dtAtualizacao | date : \'dd/MM/yyyy\'}}</td><td md-cell class=text-center>{{item.dtPrazo | date : \'dd/MM/yyyy\'}}</td><td md-cell>{{item.motivacaoNome}}</td><td md-cell>{{item.assuntoNome}}</td><td md-cell>{{item.estado}}</td><td md-cell>{{item.nomeMunicipio}}</td><td md-cell>{{scope._status[item.status]}}</td><td md-cell class=text-justify ng-if=scope.ocultaOf>{{item.protocolo}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=scope.excluirRegistro(item) ng-if=\"item.status != \'CANCELADA\' && item.status != \'SUSPENSA\' && item.status != \'EXECUTADA\' && item.status != \'EXECUTADA_POR_AGENTE_EXTERNO\' && item.status != \'EM_EXECUCAO_POR_AGENTE_EXTERNO\'\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Cancelar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.demanda.alterar({id : item.id})\" ng-if=\"item.status == \'DISPONIVEL\'\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.demanda.consultar({id : item.id})\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Visualizar</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-on-paginate=scope.filtrar ng-show=\"scope._registros.length > 0\" md-limit=scope.tamanhoLimite md-limit-options=\"[20, 50, 100]\" md-page=scope.registroInicial md-total={{scope.totalItems}} md-page-select></md-table-pagination></div></div>");
$templateCache.put("scripts-fiscalizacao/pages/gerenciadorInfracao/entregaDocumento/view/entrega-documento.tmpl.html","<div layout=row layout-xs=column><div flex><h2>Entrega de Documentos</h2></div></div><div layout=row layout-xs=column><div class=\"md-block datepicker-horario\" flex-gt-xs flex=20><label>De</label><md-datepicker-custom name=dataEmissao ng-model=scope._filtro.dataEmissao md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject></md-datepicker-custom></div><div class=\"md-block datepicker-horario\" flex-gt-xs flex=20><label>Até</label><md-datepicker-custom name=dataInicio ng-model=scope._filtro.dataEntrega md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject></md-datepicker-custom></div><md-input-container class=md-block flex-gt-xs flex=30><label><span>UF</span></label><md-select ng-model=scope._filtro.uf><md-option ng-value=item.sigla ng-repeat=\"item in scope._estados\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=30><label><span>Sanções aplicadas</span></label><md-select ng-model=scope._filtro.tipoDocumento><md-option ng-value=k ng-repeat=\"(k,v) in scope._tiposDocumento track by $index\">{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.filtrar(scope._filtro)><span>Pesquisar</span></md-button></div></div></div><div layout=row layout-align=\"center start\" ng-show=\"scope._registros.length > 0\"><div flex><md-table-container><table md-table><thead md-head md-order=scope.filtros.order md-on-reorder=scope.filtrar><tr md-row><th md-column><span>Tipo de Documento</span></th><th md-column><span>Data de Emissão</span></th><th md-column><span>Demanda</span></th><th md-column width=100px><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._registros\"><td md-cell>{{scope._tiposDocumento[item.tipoDocumento]}}</td><td md-cell>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td md-cell>{{item.relatorio.demanda.id | numeroDemanda}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=scope.initEntregaDocumento(item)><md-icon ng-bind=\"\'add\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-on-paginate=scope.filtrar ng-show=\"scope._registros.length > 0\" md-limit=scope.tamanhoLimite md-limit-options=\"[20, 50, 100]\" md-page=scope.registroInicial md-total={{scope.totalItems}} md-page-select></md-table-pagination></div></div><div ng-show=\"scope.documentoSelecionado == true;\"><form ng-cloak name=formRegistro ng-submit=scope.persistirRegistro(scope._documento) novalidate><div layout=row layout-xs=column class=m-t-15><div flex><h3>N° Demanda: {{scope._registro.relatorio.demanda.id | numeroDemanda}} N° - Relatório: {{scope._registro.relatorio.id}}</h3></div></div><div layout=row layout-xs=column><div flex><h3>Informar Entrega</h3></div></div><div layout=row layout-xs=column class=m-t-5><div class=\"md-block datepicker-horario\" flex-gt-xs flex=20><label>Data de Entrega</label><md-datepicker-custom name=dataEntrega ng-model=scope._documento.dataEntrega md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject ng-required=true></md-datepicker-custom></div><div class=\"md-block datepicker-horario\" flex-gt-xs flex=80></div></div><div layout=row layout-xs=column><md-radio-group layout=row ng-model=scope._documento.tipoEnvio class=\"md-block m-t-20 m-b-20 radio-button-column\" flex-gt-xs flex=20 required ng-change=scope.limparComprovanteEntrega()><md-radio-button layout=row ng-value=\"\'PRESENCIAL\'\" ng-model=scope._documento.tipoEnvio name=presencial class=\"md-primary m-b-15 m-t-15\">Presencial</md-radio-button><div layout=row layout-xs=column><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=scope.adicionarComprovanteEntrega($files) ng-disabled=\"scope._documento.tipoEnvio != \'PRESENCIAL\'\"><label>Adicionar Arquivo</label></md-button></div><md-radio-button layout=row ng-value=\"\'CORREIOS_AR\'\" ng-model=scope._documento.tipoEnvio name=correios class=\"md-primary m-b-15 m-t-15\">Correios/AR</md-radio-button><div layout=row layout-xs=column><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=scope.adicionarComprovanteEntrega($files) ng-disabled=\"scope._documento.tipoEnvio != \'CORREIOS_AR\'\"><label>Adicionar Arquivo</label></md-button></div><md-radio-button layout=row ng-value=\"\'DOU\'\" ng-model=scope._documento.tipoEnvio name=dou class=\"md-primary m-b-15 m-t-15\">DOU</md-radio-button><div layout=row layout-xs=column><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=scope.adicionarComprovanteEntrega($files) ng-disabled=\"scope._documento.tipoEnvio != \'DOU\'\"><label>Adicionar Arquivo</label></md-button></div></md-radio-group><div flex-gt-xs flex=50><div ng-show=\"scope._documento.tipoEnvio == \'PRESENCIAL\' && scope._documento.comprovanteDeEntrega.id\"><div layout=row layout-xs=column><md-icon ng-bind=\"\'description\'\" class=entrega-documento-syle></md-icon></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-show=\"scope._documento.tipoEnvio == \'PRESENCIAL\'\" style=\"margin-left: 15px !important\"><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope._documento.comprovanteDeEntrega.id}}><strong>{{scope._documento.comprovanteDeEntrega.nome}}</strong></a></md-input-container></div></div><div ng-show=\"scope._documento.tipoEnvio == \'CORREIOS_AR\' && scope._documento.comprovanteDeEntrega.id\"><div layout=row layout-xs=column><md-icon ng-bind=\"\'description\'\" class=entrega-documento-syle></md-icon></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-show=\"scope._documento.tipoEnvio == \'CORREIOS_AR\'\" style=\"margin-left: 15px !important\"><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope._documento.comprovanteDeEntrega.id}}><strong>{{scope._documento.comprovanteDeEntrega.nome}}</strong></a></md-input-container></div></div><div ng-show=\"scope._documento.tipoEnvio == \'DOU\' && scope._documento.comprovanteDeEntrega.id\"><div layout=row layout-xs=column><md-icon ng-bind=\"\'description\'\" class=entrega-documento-syle></md-icon></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-show=\"scope._documento.tipoEnvio == \'DOU\'\" style=\"margin-left: 15px !important\"><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope._documento.comprovanteDeEntrega.id}}><strong>{{scope._documento.comprovanteDeEntrega.nome}}</strong></a></md-input-container></div></div></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button type=submit class=\"md-raised md-primary btn-form-action m-l-15\" ng-show=!scope.apenasLeitura><span>Limpar</span></md-button><md-button type=submit class=\"md-raised md-primary btn-form-action m-l-15\"><span>Salvar</span></md-button></div></div></div></form></div>");
$templateCache.put("scripts-fiscalizacao/pages/gerenciadorInfracao/gerenciarRecurso/view/recurso.tmpl.html","<div layout=row layout-xs=column><div flex><h2>Gerenciar Recursos</h2></div></div><form ng-cloak novalidate name=formFiltro ng-submit=scope.filtrar(scope._filtro)><div layout=row layout-xs=column><div class=\"md-block datepicker-horario\" flex-gt-xs flex=20><label>De</label><md-datepicker-custom name=dataEmissao ng-model=scope._filtro.dataEmissao md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject required ng-disabled=scope.apenasLeitura></md-datepicker-custom></div><div class=\"md-block datepicker-horario\" flex-gt-xs flex=20><label>Até</label><md-datepicker-custom name=dataInicio ng-model=scope._filtro.dataEntrega md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject required ng-disabled=scope.apenasLeitura></md-datepicker-custom></div><md-input-container class=md-block flex-gt-xs flex=30><label><span>UF</span></label><md-select ng-model=scope._filtro.uf required ng-disabled=scope.apenasLeitura><md-option ng-value=item.sigla ng-repeat=\"item in scope._estados\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=30><label><span>Sanções aplicadas</span></label><md-select ng-model=scope._filtro.tipoDocumento required ng-disabled=scope.apenasLeitura><md-option ng-value=k ng-repeat=\"(k,v) in scope._tiposDocumento track by $index\">{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button type=submit class=\"md-primary md-raised m-l-15\"><span>Pesquisar</span></md-button></div></div></div></form><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head md-order=scope.filtros.order md-on-reorder=scope.filtrar><tr md-row><th md-column><span>Número do auto</span></th><th md-column><span>Data de entrega</span></th><th md-column><span>Tipo de documento</span></th><th md-column><span>Nome do autuado</span></th><th md-column width=100px><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._registros\"><td md-cell>{{item.numSancao}}</td><td md-cell>{{item.dataEntrega | date : \'dd/MM/yyyy\'}}</td><td md-cell>{{scope._tiposDocumento[item.tipoDocumento]}}</td><td md-cell>{{item.autuado.responsavel.nome}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=scope.openControlePrazoModal(item)><md-icon ng-bind=\"\'settings\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=scope.openEspelhoAutoModal(item.id)><md-icon ng-bind=\"\'search\'\"><md-tooltip>visualizar</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-on-paginate=scope.filtrar ng-show=\"scope._registros.length > 0\" md-limit=scope.tamanhoLimite md-limit-options=\"[20, 50, 100]\" md-page=scope.registroInicial md-total={{scope.totalItems}} md-page-select></md-table-pagination></div></div>");
$templateCache.put("scripts-fiscalizacao/pages/gerenciadorInfracao/sancao/view/sancao.tmpl.html","<style>.margin-lateral-40 {\n        margin-left: 40px;\n    }\n\n    .md-dialog-include {\n        max-width: 80%;\n        min-width: 65%;\n    }</style><div layout=row layout-xs=column><div flex><h2>CONSULTAR SANSÃO</h2></div></div><form ng-cloak novalidate name=formFiltro ng-submit=scope.filtrar(scope._filtro)><div layout=row layout-xs=column><div class=\"md-block datepicker-horario\" flex-gt-xs flex=20><label>De</label><md-datepicker-custom name=dataEmissao ng-model=scope._filtro.dataEmissao md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject required ng-disabled=scope.apenasLeitura></div><div class=\"md-block datepicker-horario\" flex-gt-xs flex=20><label>Até</label><md-datepicker-custom name=dataInicio ng-model=scope._filtro.dataEntrega md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject required ng-disabled=scope.apenasLeitura></md-datepicker-custom></div><md-input-container class=md-block flex-gt-xs flex=20><label><span>UF</span></label><md-select ng-model=scope._filtro.uf required ng-disabled=scope.apenasLeitura><md-option ng-value=item.sigla ng-repeat=\"item in scope._estados\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=20><label><span>Sanções aplicadas</span></label><md-select ng-model=scope._filtro.tipoDocumento ng-disabled=scope.apenasLeitura ng-change=scope.atualizarSeletorSancaoStatus() required><md-option ng-value=k ng-repeat=\"(k,v) in scope._tiposDocumento track by $index\">{{v}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=20><label><span>Status</span></label><md-select ng-model=scope._statusSancaoSelecionados multiple ng-disabled=scope.statusSomenteLeitura ng-change=scope.atualizarFiltroStatus()><md-select-header><input ng-model=scope.buscaStatus type=search style=border:0;padding-left:2px;height:48px placeholder=\"Buscar status\" id=input-busca-status class=md-text></md-select-header><md-optgroup label=Status><md-option ng-value=statusDescricao ng-repeat=\"statusDescricao in scope._statusSancaoParaSelecionar | filter: scope.buscaStatus | orderBy\">{{statusDescricao}}</md-option></md-optgroup></md-select></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button type=submit class=\"md-primary md-raised m-l-15\"><span>Limpar</span></md-button><md-button type=submit class=\"md-primary md-raised m-l-15\"><span>Pesquisar</span></md-button></div></div></div></form><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head md-order=scope.filtros.order md-on-reorder=scope.filtrar><tr md-row><th md-column><span>Número da Sanção</span></th><th md-column><span>Tipo de Documento</span></th><th md-column><span>Data de Emissão</span></th><th md-column><span>Status</span></th><th md-column><span>Demanda</span></th><th md-column><span>Número do relatório</span></th><th md-column width=100px><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._registros\"><td md-cell>{{item.numSancao}}</td><td md-cell>{{scope._tiposDocumento[item.tipoDocumento]}}</td><td md-cell>{{item.dataEmissao | date : \'dd/MM/yyyy\'}}</td><td md-cell>{{scope._statusSancaoIndice[item.statusSancao]}}</td><td md-cell>{{item.relatorio.demanda.id | numeroDemanda}}</td><td md-cell>{{item.relatorio.id}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-href=/fiscalizacao/api/documentos/download/{{item.id}} target=_blank><md-icon ng-bind=\"\'picture_as_pdf\'\"><md-tooltip>Imprimir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.gerenciadorInfracao.detalharSancao({id : item.id})\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Visualizar</md-tooltip></md-icon></md-button><md-button ng-show=scope.habilitaSuspenderEmbargo(item.statusSancao,item.tipoDocumento) class=\"md-fab md-raised md-mini\" ng-click=scope.changeState(item)><md-icon ng-bind=\"\'clear\'\"><md-tooltip>Suspender</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr></tbody></table></md-table-container><md-table-pagination md-on-paginate=scope.paginar ng-show=\"scope._registros.length > 0\" md-limit=scope.tamanhoLimite md-limit-options=\"[10, 20, 50]\" md-page=scope.registroInicial md-total={{scope.totalItems}} md-page-select></md-table-pagination></div></div><!--MODAL--><div style=\"visibility: hidden\"><div class=md-dialog-container id=myDialog style=\"overflow: auto\"><md-dialog aria-label=\"Suspender Auto de Embargo\" class=md-dialog-include><form ng-cloak name=formSuspenderEmbargo ng-submit=scope.questionarioRespondido(scope.suspenderEmbargo) novalidate><md-toolbar><div class=md-toolbar-tools><h2>Suspender Auto de Embargo</h2><span flex></span></div></md-toolbar><md-dialog-content><div class=md-dialog-content><!--                INICIO DA PERGUNTA 1--><div ng-show=!scope.suspenderEmbargo.showQuestao4><p><strong>1 - </strong>O recurso apresentado pelo autuado foi deferido?</p><md-radio-group ng-model=scope.suspenderEmbargo.recursoAutuadoDeferido ng-click=scope.regrasEmbargoQuestao1()><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><div ng-show=scope.suspenderEmbargo.recursoAutuadoDeferido class=margin-lateral-40><md-input-container style=\"margin: 0px\"><p>O auto de embargo foi anulado?</p></md-input-container><md-radio-group ng-model=scope.suspenderEmbargo.autoEmbargoAnulado ng-click=scope.regraQuestaoEmbargoAnulado()><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-input-container ng-show=scope.suspenderEmbargo.autoEmbargoAnulado class=md-block flex-gt-xs flex=25><label><span>Número do documento no SEI</span></label><input type=text ng-required=\"scope.suspenderEmbargo.autoEmbargoAnulado == true\" ng-model=scope.suspenderEmbargo.documentoSeiEmbargoAnulado ng-change=scope.consultarDocumento(scope.suspenderEmbargo.documentoSeiEmbargoAnulado) class=md-input ui-mask=9999999></md-input-container><md-radio-button ng-value=false class=md-primary>Não</md-radio-button><md-input-container ng-show=\"!scope.suspenderEmbargo.autoEmbargoAnulado\n                                    && scope.suspenderEmbargo.autoEmbargoAnulado != undefined\" class=md-block flex-gt-xs flex=25><label><span>Número do documento no SEI</span></label><input type=text ng-required=\"scope.suspenderEmbargo.autoEmbargoAnulado == false\" ng-model=scope.suspenderEmbargo.documentoSeiEmbargoNaoAnulado ng-change=scope.consultarDocumento(scope.suspenderEmbargo.documentoSeiEmbargoNaoAnulado) class=md-input ui-mask=9999999></md-input-container></md-radio-group></div><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div><div ng-show=\"!scope.suspenderEmbargo.recursoAutuadoDeferido\n                            && scope.suspenderEmbargo.recursoAutuadoDeferido != undefined\n                            && !scope.suspenderEmbargo.showQuestao4\"><p><strong>2 - </strong>Tem indicios de que a infração foi paralisada?</p><md-radio-group ng-model=scope.suspenderEmbargo.indicioInfracaoParalisada ng-click=scope.regrasEmbargoQuestao2()><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><div ng-show=scope.suspenderEmbargo.indicioInfracaoParalisada class=\"md-block margin-lateral-40\"><div><md-input-container style=\"margin: 0px\"><p>Precisará de nova vistoria?</p></md-input-container></div><md-radio-group ng-model=scope.suspenderEmbargo.novaVistoria><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><div style=\"margin-bottom: 15px\"><md-button class=\"md-primary md-raised upload-button margin-lateral-40\" ng-click=scope.novaVistoria() ngf-multiple=false ng-disabled=!scope.suspenderEmbargo.novaVistoria><label>Incluir nova Demanda</label></md-button></div><md-radio-button ng-value=false class=md-primary>Não</md-radio-button><div ng-show=\"!scope.suspenderEmbargo.novaVistoria\n                                              && scope.suspenderEmbargo.novaVistoria != undefined\"><md-input-container class=margin-lateral-40 flex-gt-xs flex=40><p>Anexar arquivos comprobatórios da motivação.</p></md-input-container><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=scope.addArquivoComprobatorio($files)><label>Adicionar Arquivo</label></md-button><md-input-container class=\"md-input-has-placeholder md-block flex-center\" flex-gt-xs flex=30 ng-show=scope.suspenderEmbargo.arquivo><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope.suspenderEmbargo.arquivo.id}}><strong>{{scope.suspenderEmbargo.arquivo.nome}}</strong></a></md-input-container><md-input-container class=\"md-block margin-lateral-40\" flex-gt-xs flex=100><label>Justificativa</label><textarea ng-model=scope.suspenderEmbargo.justificativaMotivacao ng-disabled=apenasLeitura maxlength=3000 md-maxlength=3000 rows=3 md-select-on-focus></textarea></md-input-container></div><div ng-show=scope.suspenderEmbargo.infracaoCessadaShow><md-input-container flex-gt-xs flex=40><p>A infracao foi cessada?</p></md-input-container><md-radio-group ng-model=scope.suspenderEmbargo.infracaoCessada class=margin-lateral-40><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div></md-radio-group></div><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div><div ng-show=\"!scope.suspenderEmbargo.infracaoCessada\n                                      && scope.suspenderEmbargo.infracaoCessada != undefined\n                                      || !scope.suspenderEmbargo.indicioInfracaoParalisada\n                                      && scope.suspenderEmbargo.indicioInfracaoParalisada != undefined\n                                      &&!scope.suspenderEmbargo.showQuestao4\"><p><strong>3 - </strong>Foi publicada liminar ou outra decisão judicial para paralisar a cobrança?</p><md-radio-group ng-model=scope.suspenderEmbargo.liminarDecisaoJudicialCobranca ng-click=scope.regrasEmbargoQuestao3()><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-input-container class=\"md-block margin-lateral-40\" flex-gt-xs flex=25 ng-show=scope.suspenderEmbargo.liminarDecisaoJudicialCobranca><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope.suspenderEmbargo.documentoSeiDecisaoJudicial ng-change=scope.consultarDocumento(scope.suspenderEmbargo.documentoSeiDecisaoJudicial) class=md-input ui-mask=9999999></md-input-container><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div><div ng-show=scope.suspenderEmbargo.liminarDecisaoJudicialCobranca><p><strong>4 - </strong>Houve decisão judicial favorável á União?</p><md-radio-group ng-model=scope.suspenderEmbargo.decisaoJudicialFavoravelUniao><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-input-container class=\"md-block margin-lateral-40\" flex-gt-xs flex=25 ng-show=scope.suspenderEmbargo.decisaoJudicialFavoravelUniao><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope.suspenderEmbargo.docSeiJudicialFavoravelUniao ng-change=scope.consultarDocumento(scope.suspenderEmbargo.docSeiJudicialFavoravelUniao) class=md-input ui-mask=9999999></md-input-container><md-radio-button ng-value=false class=md-primary>Não</md-radio-button><md-input-container class=\"md-block margin-lateral-40\" flex-gt-xs flex=25 ng-show=\"!scope.suspenderEmbargo.decisaoJudicialFavoravelUniao\n                                                              && scope.suspenderEmbargo.decisaoJudicialFavoravelUniao != undefined\"><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope.suspenderEmbargo.docSeiJudicialNaoFavoravelUniao ng-change=scope.consultarDocumento(scope.suspenderEmbargo.docSeiJudicialNaoFavoravelUniao) class=md-input ui-mask=9999999></md-input-container></md-radio-group></div><div ng-show=\"!scope.suspenderEmbargo.liminarDecisaoJudicialCobranca\n                                      && scope.suspenderEmbargo.liminarDecisaoJudicialCobranca != undefined\n                                      && !scope.suspenderEmbargo.showQuestao4\"><p><strong>5 - </strong>O processo foi analisado pela área de destinação e aprovado pelo Superintendente, concluindo pela possibilidade de regularização?</p><md-radio-group ng-model=scope.suspenderEmbargo.analisadoDestinacaoSuperintendente><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-input-container class=\"md-block margin-lateral-40\" flex-gt-xs flex=25 ng-show=scope.suspenderEmbargo.analisadoDestinacaoSuperintendente><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope.suspenderEmbargo.docSeiAnalisadoDestinacaoSuperintendente ng-change=scope.consultarDocumento(scope.suspenderEmbargo.docSeiAnalisadoDestinacaoSuperintendente) class=md-input ui-mask=9999999></md-input-container><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div></div></md-dialog-content><md-dialog-actions layout=row><span flex></span><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.cancel()>Voltar</md-button><md-button ng-disabled=scope.habilitaBotaoSalvar class=\"md-primary md-raised m-l-15\" type=submit>Salvar</md-button><md-button ng-disabled=!scope.habilitaBotaoAutuar class=\"md-primary md-raised m-l-15\" type=submit>Autuar no sei</md-button></md-dialog-actions></form></md-dialog></div></div>");
$templateCache.put("scripts-fiscalizacao/pages/demanda/mapa/view/demanda-mapa.tmpl.html","<form ng-cloak name=formRegistro novalidate class=mapa-padding><div layout=row layout-xs=column layout-align=end><div class=md-block flex-gt-xs flex=100><h3 style=\"float: right\">Demandas de {{scope.ano}}</h3></div></div><div class=\"botaoSetaEsquerda animate-hide painel-botoes\" ng-click=scope.buildToggler()><div class=divIconSetaEsquerda><ng-md-icon icon=keyboard_arrow_right></ng-md-icon></div></div><md-sidenav class=md-sidenav-left md-component-id=buscaleft md-is-open=scope.isOpen md-whiteframe=4 md-disable-backdrop><div class=panel-busca ng-class=\"{open: scope._demandas}\" style=\"pointer-events: auto\"><div class=\"md-block p-r-10\" flex-gt-xs flex=100><div layout=row layout-xs=column><div flex=100><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50><h3 class=m-t-20>Período</h3></div><div class=md-block flex-gt-xs flex=50><a ng-click=scope.buildToggler(); class=btn-position><ng-md-icon icon=keyboard_arrow_left></ng-md-icon></a></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>Início</span></span></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>Fim</span></span></div></div><div layout=row layout-xs=column><md-datepicker-custom name=dtPrazoIni ng-model=scope.filtros.dtPrazoIni md-max-date=scope.filtros.dtPrazoFim class=md-block flex-gt-xs flex=50 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true></md-datepicker-custom><md-datepicker-custom name=dtPrazoFim ng-model=scope.filtros.dtPrazoFim md-min-date=scope.filtros.dtPrazoIni class=md-block flex-gt-xs flex=50 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true></md-datepicker-custom></div><br><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.filtros.disponivel ng-change=scope.buscar(scope.filtros)>Disponível</md-checkbox></md-input-container><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.filtros.executada ng-change=scope.buscar(scope.filtros)>Executada</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.filtros.vinculada ng-change=scope.buscar(scope.filtros)>Vinculada a O.F.</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.filtros.emExecucaoAgenteExterno ng-change=scope.buscar(scope.filtros)>Em execução por agente externo</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.filtros.executadoAgenteExterno ng-change=scope.buscar(scope.filtros)>Executado por agente externo</md-checkbox></md-input-container></div><div layout=row layout-xs=column layout-align=end><md-button class=\"md-primary md-raised m-r-0\" ng-click=scope.buscar(scope.filtros)><span>Pesquisar</span></md-button></div></div></div><div layout=row layout-xs=column><div flex><md-table-container id=demo><table md-table><thead md-head><tr md-row><th md-column class=text-left width=70%><span>Demanda</span></th><th md-column class=text-center width=30%><span>Ações</span></th></tr></thead><tbody><tr md-row ng-repeat=\"demanda in scope.demandasAdicionadas\" style=\"height: 63px\" md-tooltip title=\"{{demanda.justificativa | stringLimit}}\"><td md-cell class=text-left>{{demanda.id | numeroDemanda}}</td><td md-cell class=text-center><a ng-click=scope.removerDemanda(demanda) class=cursor-pointer><md-tooltip md-direction=top>Remover</md-tooltip><ng-md-icon icon=delete></ng-md-icon></a></td></tr><tr md-row ng-show=\"scope.demandasAdicionadas.length == 0\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhuma Demanda adicionada!</td></tr></tbody></table></md-table-container><div layout=row layout-xs=column class=buttons-action ng-show=\"scope.demandasAdicionadas.length > 0\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.gerarOrdemFiscalizacao(scope.demandasAdicionadas) ng-show=!scope.apenasLeitura><span>Gerar OF</span></md-button></div></div></div></div></div></div></div></md-sidenav><div layout=row layout-xs=column><div class=\"md-block mapaLocalizacao main-transition\" style=\"margin: auto\" flex-gt-xs flex=100 ng-if=scope.carregarMapa><ci-mapafiscalizacao class=mapa-display legenda=true showminimapa=true visualizar=true bt-exportar=false bt-desenhar=false bt-limpar=false bt-marcar=false demandas=scope._demandas demandas-adicionadas=scope.demandasAdicionadas></ci-mapafiscalizacao><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" ui-sref=fiscalizacao.demanda.listar><span>Voltar</span></md-button></div></div></div></form>");
$templateCache.put("scripts-fiscalizacao/pages/ordemFiscalizacao/agenteExterno/views/agenteExterno-detalhar.tmpl.html","<md-tabs md-dynamic-height md-border-bottom><md-tab label=\"Cadastrar Fiscalização Executada por Agente Externo\"><form ng-cloak name=formRegistro ng-submit=scope.persistir(scope._registro) novalidate><h2 class=m-t-20 style=\"border-bottom: none\">{{scope.tituloPagina}}</h2><div layout=row layout-xs=column ng-if=scope.show><md-input-container class=md-block flex-gt-xs flex=50><label>Buscar Demanda</label><input ng-model=scope.demanda type=text></md-input-container><md-button class=\"md-raised md-primary btn-form-action\" ng-click=scope.buscarDemandas() ng-disabled=scope.apenasLeitura><span>Consultar</span></md-button><md-button class=\"md-primary btn-form-action\" ng-click=scope.buscarDemandas(true) ng-disabled=scope.apenasLeitura><span>Ver todas as demandas da minha UF</span></md-button></div><div layout=row layout-align=\"center start\" ng-if=\"scope.show && scope.showDemandas\"><div flex><md-table-container><table md-table><thead md-head md-order=scope._filtros.order md-on-reorder=scope.filtrar><tr md-row><th md-column class=text-left md-order-by=id><span>Número da Demanda</span></th><th md-column class=text-left width=7% md-order-by=dtAtualizacao><span>Última alteração</span></th><th md-column class=text-left width=7% md-order-by=dtPrazo><span>Prazo de atendimento</span></th><th md-column class=text-center width=1%></th><th md-column class=text-left width=25%><span>Motivação</span></th><th md-column class=text-left width=25%><span>Assunto</span></th><th md-column class=text-left width=12><span>Município</span></th><th md-column class=text-left width=7%><span>UF</span></th><th md-column class=text-left width=6%><span>Status</span></th><th md-column class=text-center width=10%><span>Ações</span></th></tr></thead><tbody><tr md-row ng-repeat=\"demanda in scope._demandas\" style=\"height: 63px\" md-tooltip=\"\" title=\"{{demanda.justificativa | stringLimit}}\"><td md-cell>{{demanda.id | numeroDemanda}}</td><td md-cell>{{demanda.dtAtualizacao | date:\'dd/MM/yyyy - HH:mm\'}}</td><td md-cell><span ng-show=!demanda.alertDate>{{demanda.dtPrazo | date:\'dd/MM/yyyy\'}}</span> <span ng-show=demanda.alertDate style=\"color: red\"><strong>{{demanda.dtPrazo | date:\'dd/MM/yyyy\'}}</strong></span></td><td md-cell><md-icon ng-show=demanda.alertIcon aria-label=alerta><img src=assets/img/icons/exclamation-mark.svg></md-icon></td><td md-cell>{{demanda.motivacao.nome}}</td><td md-cell>{{demanda.assunto.nome}}</td><td md-cell>{{demanda.endereco.nomeMunicipio}}</td><td md-cell>{{demanda.endereco.estado}}</td><td md-cell>{{demanda.status}}</td><td md-cell class=text-center><a ng-click=scope.adicionarDemanda(demanda) class=cursor-pointer><md-tooltip md-direction=top>Adicionar</md-tooltip><ng-md-icon icon=add_circle></ng-md-icon></a><a ng-click=scope.visualizarDemanda(demanda.id) class=cursor-pointer><md-tooltip md-direction=top>Visualizar</md-tooltip><ng-md-icon icon=search></ng-md-icon></a></td></tr><tr md-row ng-show=!scope._demandas.length style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhum resultado encontrado!</td></tr></tbody></table></md-table-container><md-table-pagination md-on-paginate=scope.buscarDemandas ng-show=\"scope._demandas.length > 0\" md-limit=scope._maxRegistros md-limit-options=scope._optionTamanho md-page=scope._registroInicial md-total={{scope._totalItems}} md-page-select></md-table-pagination></div></div><h3 ng-show=scope.dadosDemanda class=\"m-t-20 m-b-20\">Demandas adicionadas <span style=\"color: rgb(221, 44, 0); font-size: 12px\" ng-show=\"formRegistro.$submitted && !scope._registro.demandas.length\">Deve ser vinculado ao menos uma Demanda</span></h3><div ng-show=scope.dadosDemanda layout=row layout-xs=column><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-left width=15%><span>Número da Demanda</span></th><th md-column class=text-left width=15%><span>Última alteração</span></th><th md-column class=text-left width=15%><span>Prazo de atendimento</span></th><th md-column class=text-left width=20%><span>Motivação</span></th><th md-column class=text-left width=15%><span>Assunto</span></th><th md-column class=text-left width=13><span>Município</span></th><th md-column class=text-left width=7%><span>UF</span></th><th md-column class=text-center width=10% ng-if=!scope.apenasLeitura><span>Ações</span></th></tr></thead><tbody><tr md-row ng-repeat=\"demanda in scope._registro.demandas\" ng-class=justificativaDemanda style=\"height: 63px\" md-tooltip=\"\" title=\"{{demanda.justificativa | stringLimit}}\"><td md-cell class=text-left><span>{{demanda.id | numeroDemanda}}</span></td><td md-cell><span>{{demanda.dtAtualizacao | date:\'dd/MM/yyyy - HH:mm\'}}</span></td><td md-cell><span>{{demanda.dtPrazo | date:\'dd/MM/yyyy\'}}</span></td><td md-cell class=text-left><span>{{demanda.motivacao.nome}}</span></td><td md-cell><span>{{demanda.assunto.nome}}</span></td><td md-cell><span>{{demanda.endereco.nomeMunicipio}}</span></td><td md-cell><span>{{demanda.endereco.estado}}</span></td><td md-cell class=text-center ng-if=!scope.apenasLeitura><a ng-click=scope.removerDemanda(demanda) class=cursor-pointer><md-tooltip md-direction=top>Remover</md-tooltip><ng-md-icon icon=delete></ng-md-icon></a></td></tr><tr md-row ng-show=\"!scope._registro.demandas.length > 0\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhum resultado encontrado!</td></tr></tbody></table></md-table-container></div></div><br><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=66><label><span>Nome da Instituição parceira</span></label><input ng-model=scope._registro.instituicaoParceira aria-label=instituicaoParceira type=text maxlength=400 ng-disabled=\"scope.apenasLeitura || scope.nomeAutomatico\" required></md-input-container><md-input-container class=md-block flex-gt-xs flex=33><label><span>Tipo de Administração</span></label><md-select ng-model=scope._registro.tipoAdministracao ng-disabled=scope.apenasLeitura required><md-option ng-value=item aria-label=tipoAdministracao ng-repeat=\"item in scope._tipoAdministracao\">{{ item.tipoAdministracao }}</md-option></md-select></md-input-container></div><div ng-show=scope.dadosDemanda class=buttons-action ng-repeat=\"fiscalizacao in scope._registro.relatorios\"><div layout=row layout-xs=column><h3>Demanda {{fiscalizacao.demanda.id | numeroDemanda}}</h3></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=66><label><span>Upload dos arquivos de solicitação e confirmação de fiscalização para o agente externo</span></label><input ng-model=fiscalizacao.arquivoConfirmacao[0].nome style=\"height: 44px !important\" aria-label=\"arquivo de Confirmacao Solicitacao\" type=text maxlength=400 ng-disabled=true required></md-input-container><md-radio-group layout=row class=\"md-block m-b-20 fileUpload md-raised md-primary btn-form-action\" flex-gt-xs flex=33 style=\"height: 44px !important\"><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=true ngf-select=\"scope.adicionarArquivoDocumento($files, fiscalizacao)\" ng-disabled=scope.apenasLeitura><label>Carregar Arquivo</label></md-button></md-radio-group></div><div layout=row layout-xs=column class=m-b-20><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-left width=90%><span>Arquivo</span></th><th md-column width=10% ng-if=!scope.apenasLeitura><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"arquivo in fiscalizacao.arquivoConfirmacao\"><td md-cell><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=80><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{arquivo.id}}><strong>{{arquivo.nome}}</strong></a></md-input-container></td><td md-cell><div class=container-acoes><md-button class=\"md-fab md-raised md-mini\" ng-click=\"scope.excluirArquivoConfirmacaoSolicitacao($arquivo, fiscalizacao)\" ng-show=!scope.apenasLeitura><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir</md-tooltip></md-icon></md-button></div></td></tr></tbody></table></md-table-container></div></div><div layout=row layout-xs=column class=m-b-20><div class=md-block flex-gt-xs flex=100><label style=\"padding-right: 20px\"><span>A demanda foi executada?</span></label><md-radio-group ng-model=fiscalizacao.demandaExecutada ng-click=scope.changeDemandaExecutada(fiscalizacao) ng-disabled=scope.apenasLeitura required><md-radio-button ng-value=true class=\"md-primary radio-column\">Sim</md-radio-button><md-radio-button ng-value=false class=\"md-primary radio-column\">Não</md-radio-button></md-radio-group></div></div><div layout=row layout-xs=column ng-show=\"fiscalizacao.demandaExecutada == false\"><md-input-container class=md-block flex-gt-xs flex=100><label><span>Justificativa</span></label><input ng-model=fiscalizacao.justificativa aria-label=justificativaDemandaNaoExecutada type=text maxlength=500 ng-disabled=scope.apenasLeitura ng-required=\"fiscalizacao.demandaExecutada == false\"></md-input-container></div><div ng-show=\"fiscalizacao.demandaExecutada  == true\"><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=66><label><span>Upload dos arquivos referente ao relatório elaborado pelo agente externo</span></label><input ng-model=fiscalizacao.arquivoRelatorio[0].nome style=\"height: 44px !important\" aria-label=\"arquivo de relatorio do agente externo\" type=text maxlength=400 ng-disabled=true ng-required=\"fiscalizacao.demandaExecutada === true\"></md-input-container><md-radio-group layout=row class=\"md-block m-b-20 fileUpload md-raised md-primary btn-form-action\" flex-gt-xs flex=33 style=\"height: 44px !important\"><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=true ngf-select=\"scope.adicionarArquivoRelatorio($files, fiscalizacao)\" ng-disabled=scope.apenasLeitura><label>Carregar Arquivo</label></md-button></md-radio-group></div><div layout=row layout-xs=column class=m-b-20><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-left width=90%><span>Arquivo</span></th><th md-column width=10% ng-if=!scope.apenasLeitura><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"relatorio in fiscalizacao.arquivoRelatorio\"><td md-cell><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=80><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{relatorio.id}}><strong>{{relatorio.nome}}</strong></a></md-input-container></td><td md-cell><div class=container-acoes><md-button class=\"md-fab md-raised md-mini\" ng-click=\"scope.excluirArquivoRelatorioAgenteExterno($relatorio, fiscalizacao)\" ng-show=!scope.apenasLeitura><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir</md-tooltip></md-icon></md-button></div></td></tr></tbody></table></md-table-container></div></div><div layout=row layout-xs=column class=m-b-20><div class=md-block flex-gt-xs flex=100><label style=\"padding-right: 20px\"><span>Será necessário uma ação de fiscalização da SPU?</span></label><md-radio-group ng-model=fiscalizacao.fiscalizacaoSpu ng-click=scope.showAtuarButton() ng-disabled=scope.apenasLeitura ng-required=\"fiscalizacao.demandaExecutada == true\"><md-radio-button ng-value=true class=\"md-primary radio-column m-l-10\">Sim</md-radio-button><md-radio-button ng-value=false class=\"md-primary radio-column\">Não</md-radio-button></md-radio-group></div></div><div layout=row layout-xs=column class=m-b-20><div class=md-block flex-gt-xs flex=100><span class=fs-11><span>Período de Fiscalização</span></span></div></div><div layout=row layout-xs=column><div class=\"md-block datepicker-horario\"><label>Início*</label><md-datepicker flex-gt-xs flex=30 name=dataInicio ng-model=fiscalizacao.dataInicio class=md-block md-custom-mask=39/19/9999 md-custom-clean-mask=false md-custom-restrict=reject ng-disabled=scope.apenasLeitura ng-required=\"fiscalizacao.demandaExecutada === true\"></md-datepicker></div><div class=\"md-block datepicker-horario\"><label>Fim*</label><md-datepicker name=dataFim ng-model=fiscalizacao.dataFim ng-change=\"scope.validaDataFinal(fiscalizacao.dataFim, fiscalizacao)\" class=md-block flex-gt-xs flex=30 md-custom-mask=39/19/9999 md-custom-required=false md-custom-clean-mask=false md-custom-restrict=reject ng-disabled=scope.apenasLeitura ng-required=\"fiscalizacao.demandaExecutada == true\"></md-datepicker></div></div></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" ui-sref=fiscalizacao.ordemFiscalizacao.listar><span>Cancelar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.persistir(scope._registro) ng-show=scope.exibirSalvar ng-disabled=\"!scope._registro.demandas.length && scope.exibirSalvar\"><span>Salvar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.autuar(scope._registro) ng-show=scope.exibirAutuar ng-disabled=!scope._registro.demandas.length><span>Autuar no processo</span></md-button></div></div></div></form></md-tab><md-tab label=Histórico ng-click=scope.getHistorico() ng-disabled=!scope._registro.id><h3 class=\"m-b-20 m-t-20\">Histórico</h3><md-table-container><table md-table><thead md-head><tr md-row><th md-column width=20%><span>Data e Hora</span></th><th md-column width=45%><span>Descrição da Ocorrência</span></th><th md-column width=30%><span>Responsável</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._historicos\" ng-show=\"scope._historicos.length > 0\"><td md-cell>{{item.dtInclusao | date : \'dd/MM/yyyy - HH:mm\'}}</td><td md-cell><span ng-show=\"item.status.tipo != \'OCORRENCIA_AUTOMATICA\'\">{{item.status.nome}}</span> <span ng-show=\"item.status.tipo == \'OCORRENCIA_AUTOMATICA\'\">{{item.ocorrencia}}</span></td><td md-cell>{{item.nome}} {{item.cpf | brCpf}}</td></tr><tr ng-show=scope.buscandoHistorico style=\"height: 63px\"><td md-cell class=text-left colspan=100%><span>{{scope.mensagemHistorico}}</span></td></tr><tr ng-show=\"!scope.buscandoHistorico && scope._historicos.length == 0\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%><span>{{scope.mensagemHistorico}}</span></td></tr></tbody></table></md-table-container></md-tab></md-tabs>");
$templateCache.put("scripts-fiscalizacao/pages/ordemFiscalizacao/detalhar/views/ordemFiscalizacao-detalhar.tmpl.html","<md-tabs md-dynamic-height md-border-bottom><md-tab label=\"Gerar Ordem de Fiscalização\"><form ng-cloak name=formRegistro ng-submit=scope.persistir(scope._registro) novalidate><h2 class=m-t-20 style=\"border-bottom: none\">{{scope.tituloPagina}}</h2><h3 class=m-t-20 ng-show=scope.show>Agrupar Demandas para abertura de OF</h3><div layout=row layout-xs=column ng-if=scope.show><md-input-container class=md-block flex-gt-xs flex=50><label>Buscar Demanda</label><input ng-model=scope.demanda type=text></md-input-container><md-button class=\"md-raised md-primary btn-form-action\" ng-click=scope.buscarDemandas() ng-disabled=scope.apenasLeitura><span>Consultar</span></md-button><md-button class=\"md-primary btn-form-action\" ng-click=scope.buscarDemandas(true) ng-disabled=scope.apenasLeitura><span>Ver todas as demandas da minha UF</span></md-button></div><div layout=row layout-align=\"center start\" ng-if=scope.show><div flex><md-table-container><table md-table><thead md-head md-order=scope._filtros.order md-on-reorder=scope.filtrar><tr md-row><th md-column class=text-left md-order-by=id><span>Número da Demanda</span></th><th md-column class=text-left width=7% md-order-by=dtAtualizacao><span>Última alteração</span></th><th md-column class=text-left width=7% md-order-by=dtPrazo><span>Prazo de atendimento</span></th><th md-column class=text-center width=1%></th><th md-column class=text-left width=25%><span>Motivação</span></th><th md-column class=text-left width=25%><span>Assunto</span></th><th md-column class=text-left width=12><span>Município</span></th><th md-column class=text-left width=7%><span>UF</span></th><th md-column class=text-left width=6%><span>Status</span></th><th md-column class=text-center width=10%><span>Ações</span></th></tr></thead><tbody><tr md-row ng-repeat=\"demanda in scope._demandas\" style=\"height: 63px\" md-tooltip=\"\" title=\"{{demanda.justificativaHint | stringLimit}}\"><td md-cell>{{demanda.id | numeroDemanda}}</td><td md-cell>{{demanda.dtAtualizacao | date:\'dd/MM/yyyy - HH:mm\'}}</td><td md-cell><span ng-show=!demanda.alertDate>{{demanda.dtPrazo | date:\'dd/MM/yyyy\'}}</span> <span ng-show=demanda.alertDate style=\"color: red\"><strong>{{demanda.dtPrazo | date:\'dd/MM/yyyy\'}}</strong></span></td><td md-cell><md-icon ng-show=demanda.alertIcon><img src=assets/img/icons/exclamation-mark.svg></md-icon></td><td md-cell>{{demanda.motivacao.nome}}</td><td md-cell>{{demanda.assunto.nome}}</td><td md-cell>{{demanda.endereco.nomeMunicipio}}</td><td md-cell>{{demanda.endereco.estado}}</td><td md-cell>{{demanda.status}}</td><td md-cell class=text-center><a ng-click=scope.adicionarDemanda(demanda) class=cursor-pointer><md-tooltip md-direction=top>Adicionar</md-tooltip><ng-md-icon icon=add_circle></ng-md-icon></a><a ng-click=scope.visualizarDemanda(demanda.id) class=cursor-pointer><md-tooltip md-direction=top>Visualizar</md-tooltip><ng-md-icon icon=search></ng-md-icon></a></td></tr><tr md-row ng-show=!scope._demandas.length style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhum resultado encontrado!</td></tr></tbody></table></md-table-container><md-table-pagination md-on-paginate=scope.buscarDemandas ng-show=\"scope._demandas.length > 0\" md-limit=scope._maxRegistros md-limit-options=scope._optionTamanho md-page=scope._registroInicial md-total={{scope._totalItems}} md-page-select></md-table-pagination></div></div><h3 class=\"m-t-20 m-b-20\">Demandas desta OF* <span style=\"color: rgb(221, 44, 0); font-size: 12px\" ng-show=\"formRegistro.$submitted && !scope._registro.demandas.length\">Deve ser vinculado ao menos uma Demanda</span></h3><div layout=row layout-xs=column><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-left width=15%><span>Número da Demanda</span></th><th md-column class=text-left width=15%><span>Última alteração</span></th><th md-column class=text-left width=15%><span>Prazo de atendimento</span></th><th md-column class=text-left width=20%><span>Motivação</span></th><th md-column class=text-left width=15%><span>Assunto</span></th><th md-column class=text-left width=13><span>Município</span></th><th md-column class=text-left width=7%><span>UF</span></th><th md-column class=text-center width=10% ng-if=!scope.apenasLeitura><span>Ações</span></th></tr></thead><tbody><tr md-row ng-repeat=\"demanda in scope._registro.demandas\" ng-class=\"demanda.justificativaDemandaOf != null ? \'justificativaDemanda\' : \'\'\" style=\"height: 63px\" md-tooltip=\"\" title=\"{{demanda.justificativaHint | stringLimit}}\"><td md-cell class=text-left><span>{{demanda.id | numeroDemanda}}</span></td><td md-cell><span>{{demanda.dtAtualizacao | date:\'dd/MM/yyyy - HH:mm\'}}</span></td><td md-cell><span>{{demanda.dtPrazo | date:\'dd/MM/yyyy\'}}</span></td><td md-cell class=text-left><span>{{demanda.motivacao.nome}}</span></td><td md-cell><span>{{demanda.assunto.nome}}</span></td><td md-cell><span>{{demanda.endereco.nomeMunicipio}}</span></td><td md-cell><span>{{demanda.endereco.estado}}</span></td><td md-cell class=text-center ng-if=!scope.apenasLeitura><a ng-click=scope.removerDemanda(demanda) class=cursor-pointer><md-tooltip md-direction=top>Remover</md-tooltip><ng-md-icon icon=delete></ng-md-icon></a></td></tr><tr md-row ng-show=\"!scope._registro.demandas.length > 0\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhum resultado encontrado!</td></tr></tbody></table></md-table-container></div></div><h3 class=\"m-t-20 m-b-20\" ng-if=!scope.apenasLeitura>Cadastro de Equipe <span style=\"color: rgb(221, 44, 0); font-size: 12px\" ng-show=\"formRegistro.$submitted && !scope._registro.fiscais.length\">Deve ser vinculado ao menos um fiscal</span></h3><div layout=row layout-xs=column ng-if=!scope.apenasLeitura><md-input-container class=md-block flex-gt-xs flex=30><label>Fiscal</label><md-select ng-model=scope.registro.fiscal ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._fiscais\">{{ item.nome }} {{ item.cpf | brCpf}}</md-option></md-select></md-input-container><div layout=row layout-xs=column><md-button class=\"md-raised md-primary btn-form-action\" ng-click=scope.adicionarFiscal(scope.registro.fiscal)><span>Adicionar</span></md-button></div></div><h3 class=\"m-t-20 m-b-20\">Fiscais desta OF</h3><div layout=row layout-xs=column><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-left ng-show=false><span>id</span></th><th md-column class=text-left width=15%><span>CPF</span></th><th md-column class=text-left width=25%><span>Nome</span></th><th md-column class=text-left width=10%><span>Banco</span></th><th md-column class=text-left width=10%><span>Agência</span></th><th md-column class=text-left width=10%><span>Conta Corrente</span></th><th md-column class=text-center width=25% ng-if=!scope.apenasLeitura><span>Ações</span></th></tr></thead><tbody><tr md-row ng-repeat=\"fiscais in scope._registro.fiscais\" style=\"height: 63px\"><td md-cell ng-show=false>{{fiscais.id}}</td><td md-cell>{{fiscais.cpf | brCpf}}</td><td md-cell>{{fiscais.nome}}</td><td md-cell>{{fiscais.banco}}</td><td md-cell>{{fiscais.agencia}}</td><td md-cell>{{fiscais.contaCorrente}}</td><td md-cell class=text-center ng-if=!scope.apenasLeitura><a ng-click=scope.removerFiscal(fiscais) class=cursor-pointer><md-tooltip md-direction=top>Remover</md-tooltip><ng-md-icon icon=delete></ng-md-icon></a></td></tr><tr md-row ng-show=\"!scope._registro.fiscais.length > 0\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhum resultado encontrado!</td></tr></tbody></table></md-table-container></div></div><h3 class=\"m-t-20 m-b-20\">Dados Básicos da OF</h3><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50><span class=fs-11><span>Período de Fiscalização</span></span></div></div><div layout=row layout-xs=column class=m-t-10><div class=\"md-block datepicker-horario\"><label>Início*</label><md-datepicker-custom flex-gt-xs flex=100 name=inicio ng-change=scope.handleInicioFiscalizacaoSelected(scope._registro.inicio) ng-model=scope._registro.inicio md-min-date=scope.minDateInicio md-max-date=scope._registro.fim class=md-block flex-gt-xs md-custom-mask=39/19/9999 md-custom-clean-mask=false md-custom-restrict=reject ng-disabled=scope.apenasLeitura ng-required=true></md-datepicker-custom></div><md-input-container class=\"md-input-has-placeholder md-block\"><label>Hora</label><input input-horario type=text ng-model=scope._registro.horaInicio class=md-input ng-disabled=scope.apenasLeitura ng-required=true></md-input-container><div class=\"md-block datepicker-horario\"><label>Fim*</label><md-datepicker-custom name=fim ng-model=scope._registro.fim ng-change=scope.handleFimFiscalizacaoSelected(scope._registro.fim) md-min-date=\"scope._registro.inicio || scope.minDateInicio\" class=md-block flex-gt-xs md-custom-mask=39/19/9999 md-custom-required=false md-custom-clean-mask=false md-custom-restrict=reject ng-disabled=scope.apenasLeitura ng-required=true></md-datepicker-custom></div><md-input-container class=\"md-input-has-placeholder md-block\"><label>Hora</label><input input-horario type=text ng-model=scope._registro.horaFim class=md-input ng-disabled=scope.apenasLeitura ng-required=true></md-input-container></div><div layout=row layout-xs=column style=\"margin-bottom: 40px\"><div class=\"md-block validation-messages\" flex-gt-xs flex=100 style=\"color:red; font-size: 12px\"><div ng-show=\"((formRegistro.inicio.$error.valid) && (scope._registro.inicio))\">Escolha uma data válida!</div><div ng-show=formRegistro.inicio.$error.maxdate>Escolha uma data menor que a data fim!</div><div ng-show=\"((formRegistro.fim.$error.valid) && (scope._registro.fim))\">Escolha uma data válida!</div><div ng-show=formRegistro.fim.$error.mindate>Escolha uma data maior que a data de início!</div></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><label>Descrição das atividades a serem executadas</label><textarea ng-model=scope._registro.atividade maxlength=3000 md-maxlength=3000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura required></textarea></md-input-container></div><h3 class=\"m-t-20 m-b-20\">Dados do Deslocamento</h3><div layout=row layout-xs=column class=m-b-30><md-radio-group ng-model=scope._registro.viagem ng-change=scope.getMunicipios() class=md-block flex-gt-xs flex=45 ng-disabled=scope.apenasLeitura style=\"height: 20px;margin-top: 10px\" required><label class=p-r-25>Será necessário gerar PCD:</label><md-radio-button ng-value=true class=\"md-primary radio-column\">Sim</md-radio-button><md-radio-button ng-value=false class=\"md-primary radio-column\">Não</md-radio-button></md-radio-group><md-input-container class=md-block flex-gt-xs flex=40 ng-show=\"scope._registro.viagem === true\"><label><span>Municipio Origem</span></label><md-select ng-model=scope._registro.origem ng-change=scope.validaMunicipiosDestino(scope._registro.origem) ng-disabled=scope.apenasLeitura ng-required=\"scope._registro.viagem === true && !scope._registro.origem\"><md-input-container class=md-block flex-gt-xs><md-select-header><input ng-model=pesquisarMunicipioOrigem ng-click=$event.stopPropagation() ng-keydown=$event.stopPropagation() placeholder=\"Digite o Município...\" type=search></md-select-header></md-input-container><md-option ng-value=item.id ng-repeat=\"item in scope._municipiosOrigem | filter: pesquisarMunicipioOrigem\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=40 ng-show=\"scope._registro.viagem === true\"><label><span>Municipio Destino</span></label><md-select multiple ng-model=scope._registro.destinos ng-disabled=\"scope.apenasLeitura || scope.destinoCarregando\" ng-required=\"scope._registro.viagem === true\" ng-change=scope.validaQuantidadeMunicipiosDestino(true) ng-blur=scope.validaQuantidadeMunicipiosDestino(false)><md-input-container class=md-block flex-gt-xs><md-select-header><input ng-model=pesquisarMunicipioDestino ng-click=$event.stopPropagation() ng-keydown=$event.stopPropagation() placeholder=\"Digite o Município...\" type=search></md-select-header></md-input-container><md-option ng-disabled=item.checked ng-value=item ng-repeat=\"item in scope._municipiosDestino | filter: pesquisarMunicipioDestino\">{{ item.nome }}</md-option></md-select></md-input-container></div><div ng-show=\"scope._registro.viagem === true\"><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100><span class=fs-11><span>Transporte</span></span></div></div><div layout=row layout-xs=column class=m-b-30><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox ng-model=scope._registro.transporteFluvial aria-label=Fluvial ng-true-value=true ng-class=\"{\'check-box-invalid\' : !scope._registro.transporteFluvial && !scope._registro.transporteTerrestre && !scope._registro.transporteAereo}\" ng-false-value=false class=\"md-primary m-b-0\" ng-disabled=scope.apenasLeitura flex>Fluvial</md-checkbox><md-checkbox ng-model=scope._registro.transporteTerrestre aria-label=Terrestre ng-class=\"{\'check-box-invalid\' : !scope._registro.transporteFluvial && !scope._registro.transporteTerrestre && !scope._registro.transporteAereo}\" ng-true-value=true ng-false-value=false class=\"md-primary m-b-0\" ng-disabled=scope.apenasLeitura flex>Terrestre</md-checkbox><md-checkbox ng-model=scope._registro.transporteAereo aria-label=Aéreo ng-true-value=true ng-class=\"{\'check-box-invalid\' : !scope._registro.transporteFluvial && !scope._registro.transporteTerrestre && !scope._registro.transporteAereo}\" ng-false-value=false class=\"md-primary m-b-0\" ng-disabled=scope.apenasLeitura flex>Aéreo</md-checkbox></md-input-container></div><div layout=row layout-xs=column class=m-b-20><div class=md-block flex-gt-xs flex=100><span class=fs-11><span>Período de Deslocamento</span><p>{{scope._registro.inicioDeslocamento}}</p></span></div></div><div layout=row layout-xs=column><md-datepicker-custom name=inicioDeslocamento ng-model=scope._registro.inicioDeslocamento ng-change=scope.validaDataInicialDeslocamento(scope._registro.inicioDeslocamento) md-min-date=scope.dtMinInicial md-max-date=scope.dtMaxFinal class=md-block flex-gt-xs flex=30 md-custom-mask=39/19/9999 md-custom-clean-mask=false md-custom-restrict=reject ng-disabled=scope.apenasLeitura ng-required=\"scope._registro.viagem === true\"></md-datepicker-custom><md-datepicker-custom name=fimDeslocamento ng-model=scope._registro.fimDeslocamento ng-change=scope.validaDataFinalDeslocamento(scope._registro.fimDeslocamento) md-min-date=scope.dtMinInicial md-max-date=scope.dtMaxFinal class=md-block flex-gt-xs flex=30 md-custom-required=false md-custom-mask=39/19/9999 md-custom-clean-mask=false md-custom-restrict=reject ng-disabled=scope.apenasLeitura ng-required=\"scope._registro.viagem === true\"></md-datepicker-custom></div><div layout=row layout-xs=column class=m-b-20><div class=\"md-block validation-messages\" flex-gt-xs flex=30 style=\"color:red; font-size: 12px\"><div class=p-l-10 ng-show=formRegistro.inicioDeslocamento.$error.maxdate>Escolha uma data menor que a data fim da O.F</div><div class=p-l-10 ng-show=formRegistro.inicioDeslocamento.$error.mindate>Escolha uma data maior que a data de inicio da O.F</div></div><div class=\"md-block validation-messages\" flex-gt-xs flex=30 style=\"color:red; font-size: 12px\"><div class=p-l-10 ng-show=formRegistro.fimDeslocamento.$error.maxdate>Escolha uma data menor que a data fim da O.F</div><div class=p-l-10 ng-show=formRegistro.fimDeslocamento.$error.mindate>Escolha uma data maior que a data de inicio da O.F</div></div><div class=\"md-block validation-messages\" flex-gt-xs flex=40></div></div></div><div layout=row layout-xs=column class=buttons-action><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33 ng-show=\"scope._registro.validado && scope._registro.viagem\"><label>Protocolo PCD</label><input type=text ng-model=scope._registro.coProtocoloSEI class=md-input ng-disabled=true></md-input-container><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" ui-sref=fiscalizacao.ordemFiscalizacao.listar><span>Cancelar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.persistir(scope._registro) ng-if=scope.exibirBotaoGerar><span>Gerar Ordem de Fiscalização</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.rascunho(scope._registro) ng-show=\"scope._registro.demandas.length && scope.exibirBotaoRascunho\"><span>Salvar Rascunho</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.validar(scope._registro) ng-show=scope.exibirBotaoValidar><span>{{scope.tituloBotaovalidacao}}</span></md-button></div></div></div></form></md-tab><md-tab label=Histórico ng-click=scope.getHistorico() ng-disabled=!scope._registro.id><h3 class=\"m-b-20 m-t-20\">Histórico</h3><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-center width=10%><span>Data e Hora</span></th><th md-column width=45%><span>Descrição da Ocorrência</span></th><th md-column width=40%><span>Responsável</span></th><th md-column class=text-center width=5%><span>Descrição</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._historicos\" ng-show=\"scope._historicos.length > 0\"><td md-cell class=text-center>{{item.dtInclusao | date : \'dd/MM/yyyy - HH:mm\'}}</td><td md-cell><span ng-show=\"item.status.tipo != \'OCORRENCIA_AUTOMATICA\'\">{{item.status.nome}}</span> <span ng-show=\"item.status.tipo == \'OCORRENCIA_AUTOMATICA\'\">{{item.ocorrencia}}</span></td><td md-cell>{{item.nome}} {{item.cpf | brCpf}}</td><td md-cell class=text-center><a class=cursor-pointer ng-click=\"scope.showModalDetalheHistorico($event, item)\" ng-if=\"item.status.tipo != \'EXCLUSAO\'\"><md-tooltip md-direction=top>Visualizar</md-tooltip><ng-md-icon icon=search></ng-md-icon></a></td></tr><tr ng-show=scope.buscandoHistorico style=\"height: 63px\"><td md-cell class=text-left colspan=100%><span>{{scope.mensagemHistorico}}</span></td></tr><tr ng-show=\"!scope.buscandoHistorico && scope._historicos.length == 0\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%><span>{{scope.mensagemHistorico}}</span></td></tr></tbody></table></md-table-container></md-tab></md-tabs>");
$templateCache.put("scripts-fiscalizacao/pages/ordemFiscalizacao/detalhar/views/ordemFiscalizacao-historico-modal.tmpl.html","<md-dialog id=modal><div id=su-fiscalizacao><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Detalhe Histórico</span><md-button class=md-icon-button ng-click=scope.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><md-dialog-content><div class=md-dialog-content><div class=sgr-container-table layout=column><md-table-container><table md-table><thead md-head><tr md-row><th md-column width=20%><span>Campo</span></th><th md-column width=40%><span>Atual</span></th><th md-column width=40%><span>Anterior</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._detalhes\"><td md-cell>{{item.campo}}</td><td md-cell ng-if=\"item.campo == \'Processo Administrativo\'\">{{item.atual | numberProcesso }}</td><td md-cell ng-if=\"item.campo == \'Viagem\' || item.campo == \'Transporte Fluvial\' || item.campo == \'Transporte Terrestre\' || item.campo == \'Transporte Aéreo\'\">{{item.atual == \'true\' ? \'Sim\' : \'Não\' }}</td><td md-cell ng-if=\"item.campo != \'Processo Administrativo\' && item.campo != \'Viagem\' && item.campo != \'Transporte Fluvial\' && item.campo != \'Transporte Terrestre\' &&    item.campo != \'Transporte Aéreo\'\">{{item.atual}}</td><td md-cell ng-if=\"item.campo == \'Processo Administrativo\'\">{{item.anterior | numberProcesso }}</td><td md-cell ng-if=\"item.campo == \'Viagem\' || item.campo == \'Transporte Fluvial\' || item.campo == \'Transporte Terrestre\' || item.campo == \'Transporte Aéreo\'\">{{item.anterior == \'true\' ? \'Sim\' : \'Não\' }}</td><td md-cell ng-if=\"item.campo != \'Processo Administrativo\' && item.campo != \'Viagem\' && item.campo != \'Transporte Fluvial\' && item.campo != \'Transporte Terrestre\' && item.campo != \'Transporte Aéreo\'\">{{item.anterior}}</td></tr></tbody></table></md-table-container></div></div></md-dialog-content><md-dialog-actions layout=row><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=md-primary ng-click=scope.cancelModal()><md-icon md-font-set=material-icons>close</md-icon><span>Fechar</span></md-button></div></section></div></md-dialog-actions></div></md-dialog>");
$templateCache.put("scripts-fiscalizacao/pages/ordemFiscalizacao/listar/view/ordemFiscalizacao-listar.tmpl.html","<form name=formRegistro novalidate><div layout=row layout-xs=column><div flex><h2>Consulta de Ordens de Fiscalização</h2></div></div><div layout=row layout-xs=column><div flex><h3>Filtros de Pesquisa</h3></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=50></div><div class=md-block flex-gt-xs flex=50 style=\"margin-top: -15px\"><span class=datepicker-label><span>Período</span></span></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Protocolo</span></label><input ng-model=scope.filtros.protocolo type=text maxlength=100></md-input-container><md-datepicker-custom name=inicio ng-model=scope.filtros.inicio md-max-date=scope.filtros.fim class=md-block flex-gt-xs flex=25 md-custom-required=false md-custom-mask=39/19/9999 md-custom-clean-mask=false md-custom-restrict=reject ng-disabled=scope.apenasLeitura></md-datepicker-custom><md-datepicker-custom name=fim ng-model=scope.filtros.fim md-min-date=scope.filtros.inicio class=md-block flex-gt-xs flex=25 md-custom-required=false md-custom-mask=39/19/9999 md-custom-clean-mask=false md-custom-restrict=reject ng-disabled=scope.apenasLeitura></md-datepicker-custom></div><div layout=row layout-xs=column class=m-b-20><md-input-container class=md-block flex-gt-xs flex=50 ng-show=!scope.agenteExterno><md-checkbox class=\"md-primary md-block\" ng-model=scope.filtros.viagem style=\"align-self: center\" ng-disabled=scope.apenasLeitura><span>Mostrar somente ordens de fiscalização com PCD</span></md-checkbox></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label>Status</label><md-select ng-model=scope.filtros.status ng-disabled=scope.apenasLeitura><md-option ng-value=k ng-onchange=\"(k,v) in scope._status track by $index\" ng-repeat=\"(k,v) in scope._status track by $index\">{{ v }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column></div><md-input-container class=md-block flex-gt-xs flex=50><md-checkbox class=\"md-primary md-block\" ng-model=scope.agenteExterno style=\"align-self: center\" ng-blur=scope.getAgenteExterno() ng-disabled=scope.apenasLeitura><span>Agente Externo</span></md-checkbox></md-input-container><div layout=row layout-xs=column><div class=\"md-block validation-messages\" flex-gt-xs flex=50 style=\"color:red; font-size: 12px\"><div ng-show=formRegistro.inicio.$error.maxdate>Escolha uma data menor que a data fim</div><div ng-show=formRegistro.fim.$error.mindate>Escolha uma data maior que a data de inicio</div></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=50><md-button class=\"md-primary md-raised\" ui-sref=fiscalizacao.ordemFiscalizacao.incluir><md-icon md-font-set=material-icons>add</md-icon><span>Nova Ordem de Fiscalização</span></md-button></div><div class=md-block flex-gt-xs flex=50><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=scope.limparFiltros()><md-icon md-font-set=material-icons>filter_list</md-icon><span>Limpar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.filtrar()><md-icon md-font-set=material-icons>search</md-icon><span>Pesquisar</span></md-button></div></div></div></form><!--Tabela ordem Fiscalização--><div layout=row layout-align=\"center start\"><div flex><md-table-container ng-if=!scope.tabelaAgenteExterno><table md-table ng><thead md-head md-order=scope.filtros.order md-on-reorder=scope.filtrar><tr md-row><th md-column md-order-by=protocolo class=text-center width=300px><span>Protocolo da OF</span></th><th md-column md-order-by=dataEdicao class=text-center width=300px><span>Última atualização</span></th><th md-column md-order-by=inicio class=text-center><span>Prazo de antendimento</span></th><th md-column md-order-by=status><span>Status</span></th><th md-column><span>Demanda</span></th><th md-column width=100px></th><th md-column width=100px><span>Ações</span></th></tr></thead><tbody md-body ng-repeat=\"item in scope._registros\"><tr md-row><td md-cell class=text-center>{{item.protocolo}}</td><td md-cell class=text-center>{{item.dataEdicao | date : \'dd/MM/yyyy\'}}</td><td md-cell class=text-center>{{item.inicio | date : \'dd/MM/yyyy\'}} - {{item.fim | date : \'dd/MM/yyyy\'}}</td><td md-cell>{{scope._status[item.status]}}</td><td md-cell><div ng-repeat=\"demandaId in item.demandas | limitTo: 3\" ng-show=!item.exibirTodos><span class=of-lista-demanda ng-click=\"scope.getDemanda(demandaId, item.id)\">{{demandaId | numeroDemanda}}</span></div><div ng-repeat=\"demandaId in item.demandas\" ng-show=item.exibirTodos><span class=of-lista-demanda ng-click=\"scope.getDemanda(demandaId, item.id)\">{{demandaId | numeroDemanda}}</span></div></td><td md-cell><md-icon ng-bind=\"\'attach_file\'\" ng-show=item.viagem><md-tooltip>Possui PCD</md-tooltip></md-icon></td><td md-cell><div class=of-lista-total-demandas><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=scope.excluirRegistro(item) ng-if=\"item.status == \'AGUARDANDO_VALIDACAO\' || item.status == \'EM_CONSTRUCAO\'\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.ordemFiscalizacao.alterar({id : item.id})\" ng-if=\"item.status == \'AGUARDANDO_VALIDACAO\' || item.status == \'EM_CONSTRUCAO\'\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.ordemFiscalizacao.consultar({id : item.id})\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Visualizar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.ordemFiscalizacao.validar({id : item.id})\" ng-if=\"item.status == \'AGUARDANDO_VALIDACAO\' && scope._usuario.permissoes.indexOf(\'FISCALIZACAO.ORDEM_FISCALIZACAO.VALIDAR\') !== -1\"><md-icon ng-bind=\"\'check-circle\'\"><md-tooltip>Validar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.ordemFiscalizacao.revalidar({id : item.id})\" ng-if=\"item.status == \'AGUARDANDO_REVALIDACAO\' && scope._usuario.permissoes.indexOf(\'FISCALIZACAO.ORDEM_FISCALIZACAO.VALIDAR\') !== -1\"><md-icon ng-bind=\"\'check-circle\'\"><md-tooltip>Revalidar ORDEM DE FISCALIZAÇÃO</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div><md-fab-trigger ng-show=\"item.demandas.length > 3\"><md-button aria-label=menu class=md-fab ng-click=scope.handleExibirDemanda(item)><md-icon ng-bind=\"\'more_horiz\'\"></md-icon></md-button></md-fab-trigger></div></td></tr><tr md-row class=demanda-tr ng-show=\"scope.demanda.ofId === item.id\"><td md-cell colspan=100%><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-center width=10%><span>Numero da Demanda</span></th><th md-column class=text-center width=8%><span>Última atualização</span></th><th md-column class=text-center width=3%><span>Prazo de antendimento</span></th><th md-column class=text-center width=20%><span>Motivação</span></th><th md-column class=text-center><span>Assunto</span></th><th md-column class=text-center width=5%><span>UF</span></th><th md-column class=text-center width=5%><span>Município</span></th><th md-column width=10%><span>Status</span></th></tr></thead><tbody md-body><tr md-row md-tooltip title={{scope.demanda.justificativa}}><td md-cell class=text-center>{{scope.demanda.id | numeroDemanda}}</td><td md-cell class=text-center>{{scope.demanda.dtAtualizacao | date : \'dd/MM/yyyy\'}}</td><td md-cell class=text-center>{{scope.demanda.dtPrazo | date : \'dd/MM/yyyy\'}}</td><td md-cell class=text-center>{{scope.demanda.motivacaoNome}}</td><td md-cell>{{scope.demanda.assuntoNome}}</td><td md-cell class=text-center>{{scope.demanda.estado}}</td><td md-cell class=text-center>{{scope.demanda.nomeMunicipio}}</td><td md-cell>{{scope.demanda.status}}</td></tr></tbody></table></md-table-container></td></tr></tbody></table></md-table-container><!--Tabela Agente Externo--><md-table-container ng-if=scope.tabelaAgenteExterno><table md-table ng><thead md-head md-order=scope.filtros.order md-on-reorder=scope.filtrar><tr md-row><th md-column md-order-by=dataEdicao class=text-center width=300px><span>Última atualização</span></th><th md-column md-order-by=status><span>Status</span></th><th md-column><span>Demanda</span></th><th md-column width=100px><span>Ações</span></th></tr></thead><tbody md-body ng-repeat=\"item in scope._registros\"><tr md-row><td md-cell class=text-center>{{item.dataEdicao | date : \'dd/MM/yyyy\'}}</td><td md-cell>{{scope._status[item.status]}}</td><td md-cell><div ng-repeat=\"demandaId in item.demandas | limitTo: 3\" ng-show=!item.exibirTodos><span class=of-lista-demanda ng-click=\"scope.getDemanda(demandaId, item.id)\">{{demandaId | numeroDemanda}}</span></div><div ng-repeat=\"demandaId in item.demandas\" ng-show=item.exibirTodos><span class=of-lista-demanda ng-click=\"scope.getDemanda(demandaId, item.id)\">{{demandaId | numeroDemanda}}</span></div></td><td md-cell><div class=of-lista-total-demandas><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=scope.cancelarRegistro(item) ng-if=\"item.status == \'EM_EXECUCAO_POR_AGENTE_EXTERNO\'\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Cancelar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.ordemFiscalizacao.alterarAgenteExterno({id : item.id})\" ng-if=\"item.status == \'EM_EXECUCAO_POR_AGENTE_EXTERNO\'\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.ordemFiscalizacao.consultarAgenteExterno({id : item.id})\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Visualizar</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div><md-fab-trigger ng-show=\"item.demandas.length > 3\"><md-button aria-label=menu class=md-fab ng-click=scope.handleExibirDemanda(item)><md-icon ng-bind=\"\'more_horiz\'\"></md-icon></md-button></md-fab-trigger></div></td></tr><tr md-row class=demanda-tr ng-show=\"scope.demanda.ofId === item.id\"><td md-cell colspan=100%><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-center width=10%><span>Numero da Demanda</span></th><th md-column class=text-center width=8%><span>Última atualização</span></th><th md-column class=text-center width=3%><span>Prazo de antendimento</span></th><th md-column class=text-center width=20%><span>Motivação</span></th><th md-column class=text-center><span>Assunto</span></th><th md-column class=text-center width=5%><span>UF</span></th><th md-column class=text-center width=5%><span>Município</span></th><th md-column width=10%><span>Status</span></th></tr></thead><tbody md-body><tr md-row md-tooltip title={{scope.demanda.justificativa}}><td md-cell class=text-center>{{scope.demanda.id | numeroDemanda}}</td><td md-cell class=text-center>{{scope.demanda.dtAtualizacao | date : \'dd/MM/yyyy\'}}</td><td md-cell class=text-center>{{scope.demanda.dtInicio | date : \'dd/MM/yyyy\'}} - {{scope.demanda.dfFim | date : \'dd/MM/yyyy\'}}</td><td md-cell class=text-center>{{scope.demanda.motivacaoNome}}</td><td md-cell>{{scope.demanda.assuntoNome}}</td><td md-cell class=text-center>{{scope.demanda.estado}}</td><td md-cell class=text-center>{{scope.demanda.nomeMunicipio}}</td><td md-cell>{{scope.demanda.status}}</td></tr></tbody></table></md-table-container></td></tr></tbody></table></md-table-container><md-table-pagination md-on-paginate=scope.filtrar ng-show=\"scope._registros.length > 0\" md-limit=scope.tamanhoLimite md-limit-options=scope.optionTamanho md-page=scope.registroInicial md-total={{scope.totalItems}} md-page-select></md-table-pagination></div></div><style>.demanda-tr {\n        background: #FFFFFF !important;\n    }\n\n    .demanda-tr:hover {\n        background: #FFFFFF !important;\n    }</style>");
$templateCache.put("scripts-fiscalizacao/pages/ordemFiscalizacao/listar/view/ordemFiscalizacao-modal-revalidacao.tmpl.html","<md-dialog id=revalidarModal><div id=su-fiscalizacao><div class=md-toolbar-tools><span style=font-size:xx-large flex>Atenção!</span></div><md-dialog-content><div class=md-dialog-content><span style=font-size:15px flex>Deseja revalidar esta Ordem de Fiscalização?</span></div></md-dialog-content></div><md-dialog-actions layout=row><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=md-primary aria-label=Close ng-click=scope.closeDialog()><span>CANCELAR</span></md-button><md-button class=md-primary aria-label=ShowHideAccountant ng-click=scope.rejeitar(scope._registro)><span>REJEITAR</span></md-button><md-button class=md-primary aria-label=ShowHideAccountant ng-click=scope.revalidar(scope._registro)><span>REVALIDAR</span></md-button></div></section></div></md-dialog-actions></md-dialog>");
$templateCache.put("scripts-fiscalizacao/pages/painel/meta/view/meta.tmpl.html","<form name=formRegistro novalidate><div layout=row layout-xs=column><div flex><h2>Manter Metas</h2></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100><p class=meta-aviso>Atenção! O sistema somente permite a alteração das Metas até dia 31/01 do ano subsequente ao ano atual!</p></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=35></div><div class=md-block flex-gt-xs flex=30><h3>Filtros de Pesquisa</h3></div><div class=md-block flex-gt-xs flex=35></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=35></div><md-input-container class=md-block flex-gt-xs flex=30><label><span>Ano</span></label><input ng-model=scope.registro.ano type=text maxlength=4 ui-number-mask=0 ui-hide-group-sep ng-disabled=scope.anoSelecionado></md-input-container><div class=md-block flex-gt-xs flex=35></div></div><div layout=row layout-xs=column class=\"buttons-action no-border\" style=\"padding-top: 0\"><div class=md-block flex-gt-xs flex=35></div><div class=md-block flex-gt-xs flex=35 style=\"padding-right: 40px\"><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=scope.limpar()><md-icon md-font-set=material-icons>filter_list</md-icon><span>Limpar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.findBy(scope.registro.ano)><md-icon md-font-set=material-icons>search</md-icon><span>Pesquisar</span></md-button></div></div><div class=md-block flex-gt-xs flex=35></div></div><md-table-container ng-show=scope.registro.estados.length><table md-table><thead md-head><tr md-row><th md-column class=\"text-center meta-table-border meta-table-background\" width=5% rowspan=2><span><strong>Regiões</strong></span></th><th md-column class=\"text-center meta-table-border meta-table-background\" width=5% colspan=12><span>Meses</span></th></tr><tr md-row><th md-column class=\"text-center meta-table-border meta-table-background\" width=5% ng-repeat=\"mes in scope._meses\"><span><strong>{{mes.mes | mesFilter}}</strong></span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"estado in scope.registro.estados\" class=hover-none><td md-cell class=\"text-center meta-table-border hover-cell\" width=5%><strong>{{estado.sigla}}</strong></td><td md-cell class=\"text-center meta-table-border hover-cell meta-table-cell\" width=5% ng-repeat=\"mes in estado.meses\" ng-init=\"child = $index\"><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><input ng-model=scope.registro.estados[$parent.$index].meses[$index].meta aria-label=Meta ui-number-mask=0 ui-hide-group-sep type=text ng-disabled=\"scope._jurisdicao != estado.sigla || scope.apenasLeitura\"></md-input-container></div></td></tr></tbody></table></md-table-container><div layout=row layout-xs=column class=\"buttons-action m-t-15\"><div class=md-block flex-gt-xs flex=50></div><div class=md-block flex-gt-xs flex=50><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=scope.gerarCsv(scope.registro) ng-disabled=!scope.permitirAcao><span>CSV</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.limpar() ng-disabled=!scope.permitirAcao><span>CANCELAR</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.persistir(scope.registro) ng-disabled=\"scope.apenasLeitura || !scope.permitirAcao\"><span>SALVAR</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.printPdf() ng-disabled=!scope.permitirAcao><span>IMPRIMIR</span></md-button></div></div></div></form>");
$templateCache.put("scripts-fiscalizacao/pages/painel/painel-gerencial/view/painel-gerencial.tmpl.html","<form name=formRegistro ng-submit=scope.filtrar(scope.filtros) novalidate><div layout=row layout-xs=column><div flex><h2>Gráfico de total de fiscalização por UF</h2></div></div><div layout=row layout-xs=column><div flex><h3>Filtros de Pesquisa</h3></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>Período</span></span></div></div><div layout=row layout-xs=column><md-datepicker-custom name=dtInicio ng-model=scope.filtros.dtInicio md-max-date=scope.filtros.dtFim class=md-block style=\"margin-top: 4px\" flex=25 flex-xs=100 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true required></md-datepicker-custom><md-datepicker-custom name=dtFim ng-model=scope.filtros.dtFim md-min-date=scope.filtros.dtInicio class=md-block style=\"margin-top: 4px\" flex=25 flex-xs=100 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true required></md-datepicker-custom><md-input-container class=md-block flex=25 flex-xs=100><label><span>Estado</span></label><md-select ng-model=scope.filtros.estado multiple required><md-button class=layout-fill ng-click=scope.selecionaTodosEstados()>Todos</md-button><md-option ng-value=item.sigla ng-repeat=\"item in scope._estados\">{{ item.descricao }}</md-option></md-select></md-input-container><md-input-container class=md-block flex=25 flex-xs=100><label><span>Gráfico</span></label><md-select ng-model=scope.filtros.graficos multiple required><md-option ng-value=item ng-repeat=\"item in scope._tipoGraficos\">{{ item }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><div class=\"md-block validation-messages\" flex-gt-xs flex=25 style=\"color:red; font-size: 12px\"><div ng-show=formRegistro.dtInicio.$error.maxdate>Escolha uma data menor que a data fim</div></div><div class=\"md-block validation-messages\" flex-gt-xs flex=25 style=\"color:red; font-size: 12px\"><div ng-show=formRegistro.dtFim.$error.mindate>Escolha uma data maior que a data de inicio</div></div><div class=\"md-block validation-messages\" flex-gt-xs flex=50></div></div><div layout=row layout-xs=column class=\"buttons-action no-border\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=scope.limparFiltros()><md-icon md-font-set=material-icons>filter_list</md-icon><span>Limpar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=submit><md-icon md-font-set=material-icons>search</md-icon><span>Pesquisar</span></md-button></div></div></div><div layout=row layout-xs=column ng-show=scope.ufs.length><div class=md-block flex-gt-xs flex=100><div layout=row layout-xs=column><div flex><h3 style=\"color: black; margin: 0 0 20px\" class=custom-title>UF(s): {{scope.ufs}}</h3></div></div></div></div><div id=pgn-frame></div><div id=pgn-frame-mapa style=\"width: auto;height: 0\"><div id=dv-frame-mapa style=\"padding-top: 42px\"></div><div class=\"md-block mapaLocalizacao\" flex=60 flex-xs=100 ng-if=scope.isGerar><ci-mapafiscalizacao id=pdf-mapa-calor tabindex=-1 tipo=\"\'calor\'\" action=\"{{scope.apenasLeitura ? \'detail\' : \'add\'}}\"></ci-mapafiscalizacao></div></div><div layout=row layout-xs=column ng-show=scope._caracterizacao><div class=md-block flex-gt-xs flex=100><grafico-lengeda-fiscalizacao identificador=caracterizacao total=scope.totalCaracterizacao uf=scope.filtros.estado inicial=scope.filtros.dtInicio final=scope.filtros.dtFim titulo-grafico=\"Caracterização dos Imóveis Fiscalizados\" tipo=chart-pie grafico=scope._caracterizacao></grafico-lengeda-fiscalizacao></div></div><div layout=row layout-xs=column ng-show=scope._motivacao><div class=md-block flex-gt-xs flex=100><grafico-lengeda-fiscalizacao identificador=motivacao total=scope.totalMotivacao uf=scope.filtros.estado inicial=scope.filtros.dtInicio final=scope.filtros.dtFim titulo-grafico=Demanda tipo=chart-pie grafico=scope._motivacao></grafico-lengeda-fiscalizacao></div></div><div layout=row layout-xs=column ng-show=\"scope._natureza || scope._planoAnual\"><div class=md-block flex-gt-xs flex=50 ng-show=scope._natureza><grafico-lengeda-fiscalizacao identificador=natureza total=scope.totalNatureza uf=scope.filtros.estado inicial=scope.filtros.dtInicio final=scope.filtros.dtFim titulo-grafico=\"Natureza do Imóvel\" tipo=chart-pie grafico=scope._natureza></grafico-lengeda-fiscalizacao></div><div class=md-block flex-gt-xs flex=50 ng-show=scope._planoAnual><grafico-lengeda-fiscalizacao identificador=Anual total=scope.totalPlanoAnual uf=scope.filtros.estado inicial=scope.filtros.dtInicio final=scope.filtros.dtFim titulo-grafico=\"Plano Anual de Fiscalização\" tipo=chart-doughnut grafico=scope._planoAnual></grafico-lengeda-fiscalizacao></div></div><div layout=row layout-xs=column ng-show=scope._assunto><div class=md-block flex-gt-xs flex=100><grafico-lengeda-fiscalizacao identificador=assunto total=scope.totalAssunto uf=scope.filtros.estado inicial=scope.filtros.dtInicio final=scope.filtros.dtFim titulo-grafico=\"Demandas mais Executadas\" tipo=chart-bar grafico=scope._assunto></grafico-lengeda-fiscalizacao></div></div><div layout=row layout-xs=column ng-show=scope.visualizarMapa><div class=md-block flex-gt-xs flex=100 id=mapa-calor><div id=png-mapa-calor style=\"background-color: #FAFAFA;padding-top: 40px\"><div class=md-block flex-gt-xs flex=100 ng-show=scope.visualizarMapa><h3>Fiscalizações no Brasil</h3></div><div layout=row layout-xs=column><div class=\"md-block mapaLocalizacao\" flex=60 flex-xs=100 ng-if=scope.visualizarMapa><ci-mapafiscalizacao id=pdf-mapa-calor tabindex=-1 tipo=\"\'calor\'\" action=\"{{scope.apenasLeitura ? \'detail\' : \'add\'}}\"></ci-mapafiscalizacao></div><div class=md-block flex=10 flex-xs=100></div><div class=\"md-block mapaLocalizacao\" flex=30 flex-xs=100 ng-if=scope.visualizarMapa><div class=md-block flex=100 flex-xs=100><h2 class=quantidade-infracao>Total - {{scope.totalMapa}}</h2></div><div class=md-block flex=100 ng-show=scope.visualizarMapa><div ng-repeat=\"item in scope.demandasMapaCalor track by $index\" style=\"margin: 10px 0px 10px 10px\"><div class=md-block flex-gt-xs flex=100><button type=button ng-click=scope.enquadrarUf(item.uf) class=btn-quantidade-infracao>{{item.uf}} : {{item.mapaCalorDTO.length}}</button></div></div></div></div></div></div><div layout=row layout-xs=column class=\"buttons-action no-border\" style=\"padding-bottom: 0; margin-top: 197px\" ng-show=scope.visualizarMapa><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\"><span>Ver Gráfico por UFs</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.gerarPng()><span>PNG</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.printPdf()><span>PDF</span></md-button></div></div></div></div></div></form>");
$templateCache.put("scripts-fiscalizacao/pages/painel/total-fiscalizacao/view/total-fiscalizacao.tmpl.html","<form name=formRegistro ng-submit=scope.filtrar(scope.filtros) novalidate><div layout=row layout-xs=column><div flex><h2>Gráfico de total de fiscalização por UF</h2></div></div><div layout=row layout-xs=column><div flex><h3>Filtros de Pesquisa</h3></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>Período</span></span></div></div><div layout=row layout-xs=column><md-datepicker-custom name=dtInicio ng-model=scope.filtros.dtInicio md-max-date=scope.filtros.dtFim class=md-block flex-gt-xs flex=25 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true required></md-datepicker-custom><md-datepicker-custom name=dtFim ng-model=scope.filtros.dtFim md-min-date=scope.filtros.dtInicio class=md-block flex-gt-xs flex=25 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true required></md-datepicker-custom></div><div layout=row layout-xs=column><div class=\"md-block validation-messages\" flex-gt-xs flex=25 style=\"color:red; font-size: 12px\"><div ng-show=formRegistro.dtInicio.$error.maxdate>Escolha uma data menor que a data fim</div></div><div class=\"md-block validation-messages\" flex-gt-xs flex=25 style=\"color:red; font-size: 12px\"><div ng-show=formRegistro.dtFim.$error.mindate>Escolha uma data maior que a data de inicio</div></div><div class=\"md-block validation-messages\" flex-gt-xs flex=50></div></div><div layout=row layout-xs=column class=\"buttons-action no-border\"><div class=md-block flex-gt-xs flex=50><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=scope.limparFiltros()><md-icon md-font-set=material-icons>filter_list</md-icon><span>Limpar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=submit><md-icon md-font-set=material-icons>search</md-icon><span>Pesquisar</span></md-button></div></div><div class=md-block flex-gt-xs flex=50></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=30><div layout=row layout-align=\"center start\" ng-show=scope._tabela.length><div flex id=table><md-table-container><div><table md-table><thead md-head><tr md-row><th md-column class=text-center width=100px><span>UF</span></th><th md-column><span>Planejado</span></th><th md-column><span>Executado</span></th><th md-column class=text-center width=100px><span>%</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._tabela\" ng-style=\"{\'background\': (item.porcentagem || 0) < 50 ? \'#FD7D81\' : \'\'}\"><td md-cell class=text-center>{{item.uf}}</td><td md-cell class=text-right>{{item.planejado}}</td><td md-cell class=text-right>{{item.executado}}</td><td md-cell class=text-center>{{item.porcentagem | porcentagem}} %</td></tr><tr md-row style=\"background: #ECECEC;font-weight: bold\"><td md-cell class=text-center style=\"color: #0000008a\">{{scope.total.uf}}</td><td md-cell class=text-right style=\"color: #0000008a\">{{scope.total.planejado}}</td><td md-cell class=text-right style=\"color: #0000008a\">{{scope.total.executado}}</td><td md-cell class=text-center style=\"color: #0000008a\">{{scope.total.porcentagem | porcentagem}} %</td></tr></tbody></table></div></md-table-container></div></div></div><div class=md-block flex-gt-xs flex=50 style=\"padding-left: 50px\"><div id=chart style=\"background-color: #FAFAFA\"><canvas height=200px id=base2 class=\"chart chart-bar\" chart-data=scope.data chart-labels=scope.labels chart-series=scope.series chart-colors=scope.colors></canvas></div></div></div><div layout=row layout-xs=column class=\"buttons-action m-t-15\"><div class=md-block flex-gt-xs flex=50></div><div class=md-block flex-gt-xs flex=50><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=scope.gerarCsv() ng-disabled=!scope.permitirAcao><span>CSV</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.filtrar() ng-disabled=!scope.permitirAcao><span>EXCEL</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.showAlert($event) ng-disabled=!scope.permitirAcao><span>SALVAR</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.printPdf() ng-disabled=!scope.permitirAcao><span>IMPRIMIR</span></md-button></div></div></div></form>");
$templateCache.put("scripts-fiscalizacao/pages/painel/total-sancao/view/total-sancao.tmpl.html","<form name=formRegistro ng-submit=scope.filtrar(scope._filtros) novalidate><div layout=row layout-xs=column><div flex><h2>Sanções aplicadas</h2></div></div><div layout=row layout-xs=column><div flex><h3>Filtros de Pesquisa</h3></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>Período</span></span></div></div><div layout=row layout-xs=column><md-datepicker-custom name=dataInicio ng-model=scope._filtros.dataInicio class=md-block flex=25 flex-xs=100 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true ng-disabled=scope.apenasLeitura required></md-datepicker-custom><md-datepicker-custom name=dataFim ng-model=scope._filtros.dataFim class=md-block flex=25 flex-xs=100 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true ng-disabled=scope.apenasLeitura required></md-datepicker-custom><md-input-container class=md-block flex=25 flex-xs=100><label><span>UF</span></label><md-select ng-model=scope._filtros.uf multiple required ng-disabled=scope.apenasLeitura><md-option ng-value=item.sigla ng-repeat=\"item in scope._estados\">{{item.descricao}}</md-option></md-select></md-input-container><md-input-container class=md-block flex=25 flex-xs=100><label><span>Sanções aplicadas</span></label><md-select ng-model=scope._filtros.tipoDocumento multiple required ng-disabled=scope.apenasLeitura><md-option ng-value=k ng-repeat=\"(k,v) in scope._tiposDocumento track by $index\">{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column class=\"buttons-action no-border\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=scope.limparFiltros()><md-icon md-font-set=material-icons>filter_list</md-icon><span>Limpar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=submit><md-icon md-font-set=material-icons>search</md-icon><span>Pesquisar</span></md-button></div></div></div></form><div layout=row layout-xs=column ng-if=!scope.apenasLeitura ng-show=scope.quantidadeDemanda><div class=md-block flex-gt-xs flex=100><div layout=row layout-xs=column><div flex><h3 style=\"color: black; margin: 0 0 20px\" class=custom-title>Demandas Cadastradas: {{scope.quantidadeDemanda.qtdDemanda}}</h3></div></div><div layout=row layout-xs=column><div flex><h3 style=\"color: black; margin: 0 0 20px\" class=custom-title>Demandas Concluídas: {{scope.quantidadeDemanda.qtdDemandaExecutada}}</h3></div></div></div></div><div layout=row layout-xs=column ng-if=!scope.apenasLeitura ng-show=scope.ufs.length><div class=md-block flex-gt-xs flex=100><div layout=row layout-xs=column><div flex><h3 style=\"color: black; margin: 0 0 20px\" class=custom-title>UF(s): {{scope.ufs}}</h3></div></div></div></div><div ng-if=!scope.apenasLeitura><div layout=row layout-xs=column class=buttons-action ng-show=scope._notificacao.data.length><div class=md-block flex-gt-xs flex=100><div style=\"background-color: #FAFAFA\"><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100><h2 class=border-none>Notificação</h2></div></div><div layout=row layout-xs=column><div class=md-block flex=10 flex-xs=100><h2 class=quantidade-infracao>Total - {{scope.quantidadeDocumento.quantidadeNotificacao}}</h2></div><div class=md-block flex=25 flex-md=30 flex-xs=100 style=\"align-self: center\"><canvas id=canvasNotificacao height=462 width=462 class=\"chart chart-pie\" chart-data=scope._notificacao.data chart-options=scope._notificacao.options chart-labels=scope._notificacao.labels chart-colors=scope._notificacao.colors chart-series=scope._notificacao.series></canvas></div><div class=md-block flex=10 flex-xs=100></div><div class=md-block flex=50 flex-md=45 flex-xs=100 layout=row style=\"display: flex; flex-direction: column\"><div ng-repeat=\"item in scope._notificacao.series track by $index\" style=\"margin: 20px 0px 10px 10px\"><div class=md-block flex-gt-xs flex=100><span class=nivel-legenda ng-style=\"{\'background\' : scope._notificacao.colors[$index]}\"></span> <span class=nivel-legenda-title>{{$index + 1}} - {{item}} (<strong>{{scope._notificacao.data[$index]}}</strong>)</span></div></div></div></div></div></div></div><div layout=row layout-xs=column ng-if=!scope.apenasLeitura style=\"padding: 40px 0 40px\" ng-show=scope.quantidadePrazoNotificacao><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-warning\" md-tooltip title={{scope.toolTipoPendenteEntrega}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #FED23F\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20></div><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=80><span>{{scope.quantidadePrazo.qtdPendenteEntregaNotificacao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Pendente de entrega</span></div></div></div><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-error\" md-tooltip title={{scope.recursoInstancia}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #ED1C25\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20></div><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=80><span>{{scope.quantidadePrazo.qtdVencidoPrimeiraInstanciaNotificacao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Prazo vencido para o recebimento de recursos 1° instância</span></div></div></div><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-error\" md-tooltip title={{scope.segundaInstanciaRecurso}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #ED1C25\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20></div><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=80><span>{{scope.quantidadePrazo.qtdVencidoSegundaInstanciaNotificacao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Prazo vencido para o recebimento de recursos 2° instância</span></div></div></div><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-dark-error\" md-tooltip title={{scope.recurso}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #880015\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=20></div><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=100><span>{{scope.quantidadePrazo.qtdRecursoVencidoPrimeiraInstanciaNotificacao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Prazo vencido para análise de recursos de 1° instância</span></div></div></div><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-dark-error\" md-tooltip title={{scope.recurso}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #880015\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=20></div><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=100><span>{{scope.quantidadePrazo.qtdRecursoVencidoSegundaInstanciaNotificacao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Prazo vencido para análise de recursos de 2° instância</span></div></div></div><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-purple\" md-tooltip title={{scope.reconsideracao}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #A349A4\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=20></div><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=100><span>{{scope.quantidadePrazo.qtdReconsideracaoVencidoNotificacao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Com prazo vencido para reconsideração pelo superintendente</span></div></div></div></div><!-- INFRACAO --><div layout=row layout-xs=column class=buttons-action ng-show=scope._infracao.data.length><div class=md-block flex-gt-xs flex=100><div style=\"background-color: #FAFAFA\"><div layout=row layout-xs=column ng-show=scope._infracao.data.length><div class=md-block flex-gt-xs flex=100><h2 class=border-none>Autos de Infração</h2></div></div><div layout=row layout-xs=column ng-show=scope._infracao.data.length><div class=md-block flex=10 flex-xs=100><h2 class=quantidade-infracao>Total - {{scope.quantidadeDocumento.quantidadeInfracao}}</h2></div><div class=md-block flex=25 flex-md=30 flex-xs=100 style=\"align-self: center\"><canvas height=300px class=\"chart chart-pie\" chart-options=scope._infracao.options chart-data=scope._infracao.data chart-labels=scope._infracao.labels chart-colors=scope._infracao.colors chart-series=scope._infracao.series></canvas></div><div class=md-block flex=10 flex-xs=100></div><div class=md-block flex=50 flex-md=45 flex-xs=100 layout=row style=\"display: flex; flex-direction: column\"><div ng-repeat=\"item in scope._infracao.series track by $index\" style=\"margin: 20px 0px 10px 10px\"><div class=md-block flex-gt-xs flex=100><span class=nivel-legenda ng-style=\"{\'background\' : scope._infracao.colors[$index]}\"></span> <span class=nivel-legenda-title>{{$index + 1}} - {{item}} (<strong>{{scope._infracao.data[$index]}}</strong>)</span></div></div></div></div></div></div></div></div><div layout=row layout-xs=column ng-if=!scope.apenasLeitura style=\"padding: 40px 0 40px\" ng-show=scope.quantidadePrazoInfracao><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-warning\" md-tooltip title={{scope.toolTipoPendenteEntrega}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #FED23F\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20></div><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=80><span>{{scope.quantidadePrazo.qtdPendenteEntregaInfracao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Pendente de entrega</span></div></div></div><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-error\" md-tooltip title={{scope.recursoInstancia}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #ED1C25\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20></div><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=80><span>{{scope.quantidadePrazo.qtdVencidoPrimeiraInstanciaInfracao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Prazo vencido para o recebimento de recursos 1° instância</span></div></div></div><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-error\" md-tooltip title={{scope.segundaInstanciaRecurso}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #ED1C25\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20></div><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=80><span>{{scope.quantidadePrazo.qtdVencidoSegundaInstanciaInfracao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Prazo vencido para o recebimento de recursos 2° instância</span></div></div></div><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-dark-error\" md-tooltip title={{scope.recurso}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #880015\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=100><span>{{scope.quantidadePrazo.qtdRecursoVencidoPrimeiraInstanciaInfracao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Prazo vencido para análise de recursos de 1° instância</span></div></div></div><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-dark-error\" md-tooltip title={{scope.recurso}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #880015\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=100><span>{{scope.quantidadePrazo.qtdRecursoVencidoSegundaInstanciaInfracao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Prazo vencido para análise de recursos de 2° instância</span></div></div></div><div class=\"md-block prazo-padding\" flex-gt-xs flex=20><div class=\"prazo-container prazo-container-purple\" md-tooltip title={{scope.reconsideracao}}><div layout=row layout-xs=column class=\"p-l-10 p-r-10\"><div class=md-block flex-gt-xs flex=20><div class=icon-container style=\"background-color: #A349A4\"><i class=\"material-icons prazo-icon\">warning</i></div></div><div class=\"md-block container-warning-infracao\" flex-gt-xs flex=80><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao-quantidade\" flex-gt-xs flex=100><span>{{scope.quantidadePrazo.qtdReconsideracaoVencidoInfracao}}</span></div></div></div></div></div><div layout=row layout-xs=column><div class=\"md-block container-warning-infracao container-warning-infracao-texto\" flex-gt-xs flex=100><span>Com prazo vencido para reconsideração pelo superintendente</span></div></div></div></div>");
$templateCache.put("scripts-fiscalizacao/pages/relatorio/detalhar/view/relatorio-detalhar.tmpl.html","<form ng-cloak name=formRegistro ng-submit=scope.persistirRegistro(scope._registro) novalidate><div layout=row layout-xs=column><div flex><h2>Relatório de Fiscalização</h2></div></div><div ng-show=scope.mostraFiltrosIncluir><div layout=row layout-xs=column><div flex><h3>Agrupar Demandas para abertura de OF</h3></div></div><div layout=row layout-align=\"center start\" ng-show=scope.mostraFiltrosIncluir><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column md-order-by=protocolo class=text-center width=300px><span>Protocolo da OF</span></th><th md-column md-order-by=protocolo class=text-center width=300px><span>Última atualização</span></th><th md-column md-order-by=inicio class=text-center><span>Prazo de antendimento</span></th><th md-column md-order-by=status><span>Demanda</span></th><th md-column></th></tr></thead><tbody md-body ng-repeat=\"ordemFiscalizacao in scope._ordemFiscalizacao\"><tr md-row ng-class=\"ordemFiscalizacao.showDemandas == true ? \'selected-demanda-tr\' : \'\'\"><td md-cell class=text-center ng-click=scope.showDemandas(ordemFiscalizacao)>{{ordemFiscalizacao.protocolo}}</td><td md-cell class=text-center ng-click=scope.showDemandas(ordemFiscalizacao)>{{ordemFiscalizacao.dataEdicao | date : \'dd/MM/yyyy\'}}</td><td md-cell class=text-center ng-click=scope.showDemandas(ordemFiscalizacao)>{{ordemFiscalizacao.inicio | date : \'dd/MM/yyyy\'}} - {{ordemFiscalizacao.fim | date : \'dd/MM/yyyy\'}}</td><td md-cell><div ng-repeat=\"demanda in ordemFiscalizacao.demandas\" ng-click=scope.showDemandas(ordemFiscalizacao)><span class=of-lista-demanda>{{demanda.id | numeroDemanda}}</span></div></td><td md-cell><md-button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=\"scope.showModalJustificativaNovaDemanda($event, ordemFiscalizacao)\"><span>Nova Demanda</span></md-button></td><td md-cell></td></tr><tr md-row class=demanda-tr ng-show=\"ordemFiscalizacao.showDemandas == true\"><td md-cell colspan=100%><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-center width=10%><span>Numero da Demanda</span></th><th md-column class=text-center width=8%><span>Última atualização</span></th><th md-column class=text-center width=3%><span>Prazo de antendimento</span></th><th md-column class=text-center width=20%><span>Motivação</span></th><th md-column class=text-center><span>Assunto</span></th><th md-column class=text-center width=5%><span>UF</span></th><th md-column class=text-center width=5%><span>Município</span></th><th md-column width=10%><span>Status</span></th></tr></thead><tbody md-body ng-repeat=\"relatorio in scope._relatorios\"><tr md-row md-tooltip title={{scope.demanda.justificativa}}><td md-cell class=text-center>{{relatorio.demanda.id | numeroDemanda}}</td><td md-cell class=text-center>{{relatorio.demanda.dtAtualizacao | date : \'dd/MM/yyyy\'}}</td><td md-cell class=text-center>{{relatorio.demanda.dtPrazo | date : \'dd/MM/yyyy\'}}</td><td md-cell class=text-center>{{relatorio.demanda.motivacaoNome}}</td><td md-cell>{{relatorio.demanda.assuntoNome}}</td><td md-cell class=text-center>{{relatorio.demanda.endereco.estado}}</td><td md-cell class=text-center>{{relatorio.demanda.endereco.nomeMunicipio}}</td><td md-cell><span ng-show=!relatorio.demanda.showNovoRelatorio>{{relatorio.demanda.status}}</span><md-button ng-show=relatorio.demanda.showNovoRelatorio type=button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=\"scope.handleDemandaSelected(ordemFiscalizacao, relatorio.demanda)\"><span>Novo Relatório</span></md-button></td></tr></tbody></table></md-table-container></td></tr></tbody></table></md-table-container></div></div><div layout=row layout-xs=column style=\"display: flex; flex-direction: row-reverse\"><md-radio-group layout=row ng-model=scope._registro.isExecutada class=\"md-block m-t-20 m-b-20 pull-right\" flex-gt-xs flex=30 ng-disabled=(scope.apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-show=\"scope.demandaSelected == true\"><label class=p-r-25 style=\"margin-left: auto\">Demanda foi executada?</label><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false ng-click=\"scope.gerarRelatorio = false\" style=\"outline: none\" class=md-primary>Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-if=!scope.apenasLeitura class=buttons-action ng-show=\"scope.demandaSelected == true\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-raised md-primary btn-form-action\" ui-sref=fiscalizacao.relatorio.listar><span>Voltar</span></md-button><md-button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=scope.showModalJustificativa($event,scope._registro) ng-disabled=(scope._registro.isExecutada)><span>Concluir</span></md-button><md-button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=scope.gerar(scope._registro) ng-disabled=(!scope._registro.isExecutada)><span>Gerar Relatório</span></md-button></div></div></div></div><md-tabs md-dynamic-height md-border-bottom ng-show=scope.gerarRelatorio md-selected=tabIndex><md-tab label=\"Informações da Ordem de Fiscalização\"><ng-form name=formInfoOrdemFiscalizacao novalidate isolate-form><div ng-show=scope.gerarRelatorio class=m-t-20><div layout=row layout-xs=column><div flex><h3>Informações da Ordem de Fiscalização</h3></div></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Ordem Fiscalização</label><input type=text ng-model=scope._registro.ordemFiscalizacao.protocolo ng-disabled=true class=md-input></md-input-container><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data da Fiscalização</label><md-datepicker-custom name=dtFiscalizacao md-max-date=scope.maxDataFiscalizacao md-min-date=scope.minDataFiscalizacao ng-model=scope._registro.dtFiscalizacao md-custom-required=true md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject ng-disabled=scope.apenasLeitura></md-datepicker-custom><div class=\"md-block validation-messages\" flex-gt-xs flex=100| style=\"color:red; font-size: 12px\"><div ng-show=formRegistro.dtFiscalizacao.$error.maxdate>Escolha uma data menor que a data fim</div><div ng-show=formRegistro.dtFiscalizacao.$error.mindate>Escolha uma data maior que a data de inicio</div></div></div></div><div layout=row layout-xs=column><h3>Informações da Demanda</h3></div><div layout=row layout-xs=column><h4 class=m-t-0>Ocupante Cadastrado</h4></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>CNPJ/CPF do responsável</label><input ui-br-cpfcnpj-mask type=text ng-model=scope._registro.demanda.responsavel.cpfCnpj ng-blur=validarCpfResponsavelDemanda(scope._registro.demanda.responsavel.cpfCnpj) class=md-input ng-disabled=true></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nome do responsável</label><input type=text ng-model=scope._registro.demanda.responsavel.nome class=md-input ng-disabled=scope.demandaNomeAutomatico></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>UF</label><input type=text ng-model=scope._registro.demanda.endereco.estado class=md-input ng-disabled=true></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50 ng-if=!scope._registro.demanda.edesc></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50 ng-if=scope._registro.demanda.edesc><label>EDESC</label><input type=text ng-model=scope._registro.demanda.edesc.nucleo class=md-input ng-disabled=true></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Área do imóvel (m²)</label><input type=text ui-number-mask ng-model=scope._registro.demanda.areaImovel class=md-input ng-disabled=true></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>CEP</label><input type=text ui-br-cep-mask ng-model=scope._registro.demanda.endereco.cep class=md-input ng-disabled=true></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Município</label><input type=text ng-model=scope._registro.demanda.endereco.nomeMunicipio class=md-input ng-disabled=true></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Bairro/Distrito</label><input type=text ng-model=scope._registro.demanda.endereco.bairro class=md-input ng-disabled=true></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Tipo de logradouro</label><input type=text ng-model=scope._registro.demanda.endereco.tipoLogradouro ng-disabled=true class=md-input ng-disabled=true></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Logradouro</label><input type=text ng-model=scope._registro.demanda.endereco.logradouro class=md-input ng-disabled=true></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nº</label><input type=text ng-model=scope._registro.demanda.endereco.numero class=md-input ng-disabled=true></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Complemento</label><input type=text ng-model=scope._registro.demanda.endereco.complemento class=md-input ng-disabled=true></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Natureza do imóvel</label><input type=text ng-model=scope._registro.demanda.endereco.zoneamento.nome class=md-input ng-disabled=true></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa</label><textarea ng-model=scope._registro.demanda.justificativa style=\"opacity: 0.6\" maxlength=3000 md-maxlength=3000 rows=5 md-select-on-focus ng-disabled=true ng-readonly=true>\n                            </textarea></md-input-container></div><div layout=row layout-xs=column><h3 class=m-t-20>Informações da Fiscalização</h3></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Destinação</span></label><md-select ng-model=scope._registro.tipoImovel ng-disabled=\"scope.apenasLeitura || scope.executado || scope._registro.autuados.length > 0\" ng-required=true ng-change=scope.verificaOcorrenciaInfracao()><md-option ng-value=item ng-repeat=\"item in scope._tiposImovel\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><label>Benfeitorias encontradas</label><textarea ng-model=scope._registro.benfeitoria maxlength=3000 md-maxlength=3000 rows=5 md-select-on-focus ng-required=true ng-disabled=\"scope.executado || scope.apenasLeitura\"></textarea></md-input-container></div><div layout=row layout-xs=column><md-radio-group layout=row ng-model=scope._registro.confirmaResponsavel ng-change=scope.handleOcupanteSelected() class=\"md-block m-t-20 m-b-20\" flex-gt-xs flex=30 style=\"height: 20px;margin-top: 10px\" ng-disabled=scope.apenasLeitura><label class=p-r-25>Confirma dados do ocupante?</label><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=!scope._registro.confirmaResponsavel><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>CNPJ/CPF do responsável</label><input ui-br-cpfcnpj-mask type=text ng-model=scope._registro.responsavel.cpfCnpj ng-blur=validarCpfOcupante(scope._registro.responsavel.cpfCnpj) class=md-input ng-required=scope.obrigatoriedadeOcupante ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nome do responsável</label><input type=text ng-model=scope._registro.responsavel.nome class=md-input maxlength=100 ng-maxlength=100 ng-required=scope.obrigatoriedadeOcupante ng-disabled=\"scope.apenasLeitura || scope.ocupanteNomeAutomatico\"></md-input-container></div><div layout=row layout-xs=column><md-radio-group layout=row ng-model=scope._registro.confirmaEndereco ng-change=scope.handleEnderecoSelected() class=\"md-block m-t-20 m-b-20\" flex-gt-xs flex=30 ng-disabled=scope.apenasLeitura style=\"height: 20px;margin-top: 10px\"><label class=p-r-25>Confirma o endereço?</label><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"!scope._registro.confirmaEndereco && !scope.apenasLeitura\"><md-input-container class=\"md-block p-r-10\" flex-gt-xs flex=50><label><span>UF</span></label><md-autocomplete ng-disabled=scope.apenasLeitura md-selected-item=scope._registro.endereco.estado md-search-text=scope.filtroEstado md-selected-item-change=scope.getMunicipios(scope._registro.endereco.estado) md-items=\"item in scope._usuario.jurisdicoes |filter: scope.filtroEstado | orderBy\" md-item-text=item md-dropdown-position=bottom md-min-length=0 md-max-lenght=3 md-escape-options=clear placeholder=\"Digite a uf...\"><md-item-template><span md-highlight-text=scope.filtroEstado md-highlight-flags=^i>{{item}}</span></md-item-template><md-not-found>Nenhuma UF Encontrada...</md-not-found></md-autocomplete></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50 ng-if=\"scope._registro.demanda.edesc || scope.apenasLeitura\"><label>EDESC</label><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><md-select ng-model=scope._registro.edesc ng-disabled=\"scope.executado || scope.apenasLeitura\"><md-option ng-value=item ng-repeat=\"item in scope._nucleoEdesc\">{{ item.nucleo}}</md-option></md-select></md-input-container></div></md-input-container></div><div layout=row layout-xs=column ng-show=\"!scope._registro.confirmaEndereco  && scope.apenasLeitura\"><md-input-container class=\"md-block p-r-10\" flex-gt-xs flex=50><label><span>UF</span></label><input type=text ng-model=scope._registro.endereco.estado class=md-input ng-disabled=true></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50 ng-if=\"scope._registro.demanda.edesc || scope.apenasLeitura\"><label>EDESC</label><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><md-select ng-model=scope._registro.edesc ng-disabled=\"scope.executado || scope.apenasLeitura\"><md-option ng-value=item ng-repeat=\"item in scope._nucleoEdesc\">{{ item.nucleo}}</md-option></md-select></md-input-container></div></md-input-container></div><div layout=row layout-xs=column ng-show=!scope._registro.confirmaEndereco><md-input-container class=md-block flex-gt-xs flex=50><label>Área do imóvel (m²)</label><input type=text ui-number-mask ng-model=scope._registro.areaImovel class=md-input ng-required=!scope._registro.confirmaEndereco ng-disabled=\"scope.executado || scope.apenasLeitura\"></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label>CEP</label><input type=text ng-model=scope._registro.endereco.cep ng-blur=scope.getCEP(scope._registro.endereco.cep) ui-br-cep-mask class=md-input ng-required=!scope._registro.confirmaEndereco ng-disabled=\"scope.executado || scope.apenasLeitura\"></md-input-container></div><div layout=row layout-xs=column ng-show=\"!scope._registro.confirmaEndereco && !scope.apenasLeitura\"><md-input-container class=md-block flex-gt-xs flex=50><label><span>Município</span></label><md-autocomplete md-no-cache=noCache ng-disabled=\"(scope.apenasLeitura || !scope._registro.endereco.estado)\" ng-required=!scope._registro.confirmaEndereco md-search-text=scope.filtroMunicipio md-selected-item=scope._registro.endereco.municipio md-items=\"item in scope._municipios |filter: scope.filtroMunicipio\" md-item-text=item.nome md-dropdown-position=bottom md-min-length=0 md-max-lenght=3 md-escape-options=clear placeholder=\"Digite o município...\"><md-item-template><span md-highlight-text=scope.filtroMunicipio md-highlight-flags=^i>{{item.nome}}</span></md-item-template><md-not-found>Nenhum Município Encontrado...</md-not-found></md-autocomplete></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Bairro/Distrito</label><input type=text ng-model=scope._registro.endereco.bairro class=md-input ng-required=!scope._registro.confirmaEndereco maxlength=100 ng-maxlength=100 ng-disabled=\"scope.executado || scope.apenasLeitura\"></md-input-container></div><div layout=row layout-xs=column ng-show=\"!scope._registro.confirmaEndereco  && scope.apenasLeitura\"><md-input-container ng-if=scope.apenasLeitura class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Município</label><input type=text ng-model=scope._registro.demanda.endereco.nomeMunicipio class=md-input ng-disabled=true></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Bairro/Distrito</label><input type=text ng-model=scope._registro.endereco.bairro class=md-input ng-required=!scope._registro.confirmaEndereco maxlength=100 ng-maxlength=100 ng-disabled=\"scope.executado || scope.apenasLeitura\"></md-input-container></div><div layout=row layout-xs=column ng-show=!scope._registro.confirmaEndereco><md-input-container class=md-block flex-gt-xs flex=50><label><span>Tipo de logradouro</span></label><md-select ng-model=scope._registro.endereco.tipoLogradouro ng-disabled=\"scope.executado || scope.apenasLeitura\" ng-required=!scope._registro.confirmaEndereco><md-optgroup labe=scope.tipoLogradouro><md-option ng-value=k ng-repeat=\"(k,y) in scope._tipoLogradouro\">{{y}}</md-option></md-optgroup></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Logradouro</label><input type=text ng-model=scope._registro.endereco.logradouro class=md-input ng-required=!scope._registro.confirmaEndereco maxlength=100 ng-maxlength=100 ng-disabled=\"scope.executado || scope.apenasLeitura\"></md-input-container></div><div layout=row layout-xs=column ng-show=!scope._registro.confirmaEndereco><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nº</label><input type=text ng-model=scope._registro.endereco.numero class=md-input ng-required=!scope._registro.confirmaEndereco ng-disabled=\"scope.executado || scope.apenasLeitura\"></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Complemento</label><input type=text ng-model=scope._registro.endereco.complemento maxlength=100 ng-maxlength=100 class=md-input ng-disabled=\"scope.executado || scope.apenasLeitura\"></md-input-container></div><div layout=row layout-xs=column ng-show=!scope._registro.confirmaEndereco><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Natureza do imóvel</label><md-select ng-model=scope._registro.endereco.zoneamento ng-disabled=\"scope.executado || scope.apenasLeitura\"><md-option ng-value=item ng-repeat=\"item in scope._zoneamentos\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><label>Descritivo geral</label><textarea ng-model=scope._registro.descritivo maxlength=3000 md-maxlength=3000 rows=5 md-select-on-focus ng-required=scope.gerarRelatorio ng-disabled=\"scope.executado || scope.apenasLeitura\">\n                            </textarea></md-input-container></div><div layout=row layout-xs=column><h3 class=m-t-20>Relatório fotográfico</h3></div><div layout=row layout-xs=column class=buttons-action style=\"border: none\"><div class=md-block flex-gt-xs flex=50><md-button class=\"md-primary md-raised\" ngf-multiple=true ngf-select=scope.adicionarRelatorioFotografico($files) ngf-pattern=\"\'image/*\'\" ngf-accept=\"\'image/*\'\" ng-show=!scope.apenasLeitura><md-icon md-font-set=material-icons>add</md-icon><span>Selecionar imagem</span></md-button></div></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column><span>Nome</span></th><th md-column width=100px><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._registro.relatoriosFotograficos\"><td md-cell><span ng-show=!item.$update>{{item.nome}}</span><md-input-container class=\"md-input-has-placeholder md-block input-inline\" flex-gt-xs flex=50 ng-show=item.$update><input type=text ng-model=item.nome maxlength=100 ng-maxlength=100 class=md-input ng-disabled=scope.apenasLeitura></md-input-container></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=scope.excluirRelatorioFotografico($index) ng-show=!scope.apenasLeitura><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{item.id}}><md-icon ng-bind=\"\'file_download\'\"><md-tooltip>Download</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"item.$update = true\" ng-if=!scope.apenasLeitura ng-show=!item.$update><md-icon ng-bind=\"\'create\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"item.$update = false\" ng-show=item.$update><md-icon ng-bind=\"\'check\'\"><md-tooltip>Concluir</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr><tr md-row ng-show=\"!scope._registro.relatoriosFotograficos.length > 0\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhuma imagem encontrada!</td></tr></tbody></table></md-table-container></div></div><div class=relatorio-fotografico-content><div class=relatorio-fotografico ng-repeat=\"imagen in scope._registro.relatoriosFotograficos\"><img class=relatorio-fotografico ng-src=data:image/png;base64,{{imagen.binario}}></div></div><div layout=row layout-xs=column><h3 class=m-t-20>Croqui</h3></div><div layout=row layout-xs=column><md-radio-group layout=row ng-model=scope.tipoArquivo ng-change=scope.handleCroquiSelected(scope.tipoArquivo) class=\"md-block m-t-20 m-b-20 radio-button-column\" flex-gt-xs flex=10 ng-disabled=scope.apenasLeitura><md-radio-button layout=row ng-value=\"\'croqui\'\" ng-model=scope.tipoArquivo ng-checked=\"(scope.tipoArquivo == croqui)\" name=croqui class=\"md-primary m-b-15 m-t-15\">Importar Croqui</md-radio-button><md-radio-button layout=row ng-value=\"\'shapeFile\'\" ng-model=scope.tipoArquivo ng-checked=\"(scope.tipoArquivo == shapeFile)\" name=shapeFile class=\"md-primary m-b-15 m-t-15\">Importar Shape File</md-radio-button><md-radio-button layout=row ng-value=\"\'desenho\'\" ng-model=scope.tipoArquivo ng-checked=\"(scope.tipoArquivo == desenho)\" name=desenho class=\"md-primary m-b-15 m-t-15\">Desenhar</md-radio-button></md-radio-group><div flex-gt-xs flex=90><div layout=row layout-xs=column><md-radio-group layout=row class=\"md-block m-b-0 fileUpload md-raised md-primary btn-form-action\" flex-gt-xs flex=15 style=\"height: 44px !important; margin-top: 17px !important\"><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=scope.adicionarCroqui($files) ng-disabled=\"scope.apenasLeitura || scope.tipoArquivo != \'croqui\'\"><label>Carregar Croqui</label></md-button></md-radio-group><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-show=\"scope.tipoArquivo == \'croqui\'\"><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope._registro.arquivoCroqui.id}}><strong>{{scope._registro.arquivoCroqui.nome}}</strong></a></md-input-container></div><div layout=row layout-xs=column><md-radio-group layout=row class=\"md-block m-b-0 fileUpload md-raised md-primary btn-form-action\" flex-gt-xs flex=15 style=\"height: 44px !important\"><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=scope.adicionarShapeFile($files) ng-disabled=\"scope.apenasLeitura || scope.tipoArquivo != \'shapeFile\'\"><label>Carregar Shape File</label></md-button></md-radio-group><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-show=\"scope.tipoArquivo == \'shapeFile\'\"><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope._registro.arquivoCroqui.id}}><strong>{{scope._registro.arquivoCroqui.nome}}</strong></a></md-input-container></div></div></div><!-- Form Pesquisa Mapa --><div layout=row layout-xs=column ng-show=\"scope.tipoArquivo == \'desenho\'\"><md-input-container class=\"md-block p-r-10\" flex-gt-xs flex=20><label><span>UF</span></label><md-autocomplete ng-disabled=scope.apenasLeitura md-selected-item=scope._registro.estadoMapa md-search-text=scope.filtroEstadoMapa md-selected-item-change=scope.getMunicipiosPesquisaMapa(scope._registro.estadoMapa) md-items=\"item in scope._usuario.jurisdicoes |filter: scope.filtroEstadoMapa | orderBy\" md-item-text=item md-dropdown-position=bottom md-min-length=0 md-max-lenght=3 md-escape-options=clear placeholder=\"Digite a uf...\"><md-item-template><span md-highlight-text=scope.filtroEstadoMapa md-highlight-flags=^i>{{item}}</span></md-item-template><md-not-found>Nenhuma UF Encontrada...</md-not-found></md-autocomplete></md-input-container><md-input-container class=md-block flex-gt-xs flex=25><label><span>Município</span></label><md-autocomplete ng-disabled=scope.apenasLeitura md-selected-item=scope._registro.municipioMapa md-no-cache=true md-search-text=scope.filtroMunicipioMapa md-items=\"item in scope._municipiosMapa | filter: scope.filtroMunicipioMapa\" md-item-text=item.nome md-dropdown-position=bottom md-min-length=0 md-max-lenght=3 md-escape-options=clear md-selected-item-change=\"scope.onChangeMunicipioPesquisa(scope._registro.estadoMapa, scope._registro.municipioMapa)\" placeholder=\"Digite o município...\"><md-item-template><span md-highlight-text=scope.filtroMunicipioMapa md-highlight-flags=^i>{{item.nome}}</span></md-item-template><md-not-found>Nenhum Município Encontrado...</md-not-found></md-autocomplete></md-input-container></div><div layout=row layout-xs=column><div class=\"md-block mapaLocalizacao\" flex-gt-xs flex=50 ng-if=scope.gerarRelatorio><ci-mapafiscalizacao showminimapa=true heightminimapa=50 widthminimapa=50 tabindex=-1 latitude=scope._registro.latitude longitude=scope._registro.longitude exportar-mapa=scope.exportarKml(kml) validar=scope.validaPonto(imovel) validar-poligono=scope.validaPoligono(imovel) action=\"{{scope.apenasLeitura ? \'detail\' : \'add\'}}\" habilitar-desenho=true bt-exportar=false bt-limpar=!scope.apenasLeitura bt-marcar=!scope.apenasLeitura croqui=scope._registro.croqui terrenoshapefile=scope._arquivoCroqui.geometry bt-desenhar=scope.btDesenhar visualizar=false img-mapa=scope.imgMapa exportar-imagem=true></ci-mapafiscalizacao></div></div><div layout=row layout-xs=column ng-show=scope.mostrarOcorrenciaInfracao><h3 class=m-t-20>Ocorrência da Infração</h3></div><div layout=row layout-xs=column ng-show=scope.mostrarOcorrenciaInfracao><md-radio-group layout=row ng-model=scope._registro.ocorrenciaDeInfracao ng-change=scope.handleOcorrenciaDeInfracao(scope._registro.ocorrenciaDeInfracao) class=\"md-block m-t-20 m-b-20\" flex-gt-xs flex=30 ng-disabled=scope.apenasLeitura required style=\"height: 20px;margin-top: 10px\"><label class=p-r-25>Ocorreu infração?</label><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=md-primary>Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column class=m-t-20><md-input-container class=md-block flex-gt-xs flex=100><label>Encaminhamentos</label><textarea ng-model=scope._registro.encaminhamento ng-disabled=\"scope.executado || scope.apenasLeitura\" maxlength=3000 md-maxlength=3000 rows=5 md-select-on-focus></textarea></md-input-container></div><div layout=row layout-xs=column><h3 class=m-t-20>Membros da equipe</h3></div><div ng-repeat=\"item in scope._fiscais\"><div layout=row layout-xs=column><p><strong>{{item.nome}} | {{item.siape}}</strong><br></p></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-raised md-primary btn-form-action\" ui-sref=fiscalizacao.relatorio.listar><span>Cancelar</span></md-button><md-button type=button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=scope.salvarTabInfoOrdemFiscalizacao(scope._registro) ng-show=!scope.apenasLeitura><span>Salvar</span></md-button><md-button type=button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=scope.nextTab() ng-show=\"!scope.apenasLeitura && scope._registro.ocorrenciaDeInfracao\"><span>Continuar</span></md-button></div></div></div></div></ng-form></md-tab><md-tab label=\"Cadastrar Autuado\" ng-if=\"scope._registro.ocorrenciaDeInfracao == true && scope._registro.tipoImovel\"><ng-form name=formCadastroAutuado novalidate isolate-form><div layout=row layout-xs=column class=m-t-20><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=30 ng-class=\"scope.autuadoCpfCnpjInvalido == true ? \'md-input-invalid\' : \'\'\"><label>*CPF/CNPJ</label><input type=text ng-model=scope._autuado.responsavel.cpfCnpj ng-change=scope.validaFormAutuado(scope._autuado) ui-br-cpfcnpj-mask class=md-input ng-blur=validarCpfAutuado(scope._autuado.responsavel.cpfCnpj); ng-disabled=\"scope.autuadoBlock || scope._autuado.autuadoNaoIdentificado\"></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=70 ng-class=\"scope.autuadoNomeInvalido == true ? \'md-input-invalid\' : \'\'\"><label>*Nome</label><input type=text ng-model=scope._autuado.responsavel.nome ng-change=scope.validaFormAutuado(scope._autuado) ng-disabled=\"scope.autuadoBlock || scope.autuadoNomeAutomatico || scope._autuado.autuadoNaoIdentificado\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=30 ng-class=\"scope.autuadoProcessoSeiInvalido == true ? \'md-input-invalid\' : \'\'\"><label>{{scope._autuado.processoAdministrativo ? \'\' : \'*\'}}Processo SEI</label><input type=text ng-model=scope._autuado.processoSEI class=md-input ui-mask=99999.999999/9999-99 ng-change=scope.validaFormAutuado(scope._autuado) ng-disabled=\"scope.autuadoBlock || scope._autuado.processoAdministrativo || scope._autuado.autuadoNaoIdentificado\"></md-input-container><md-checkbox class=\"m-b-30 md-primary\" ng-model=scope._autuado.processoAdministrativo style=\"align-self: center\" ng-disabled=\"scope.autuadoBlock || scope._autuado.autuadoNaoIdentificado\" flex-gt-xs flex=70 ng-change=scope.validarProcessoSEI(scope._autuado.processoAdministrativo)><span>NÃO EXISTE PROCESSO ADMINISTRATIVO CRIADO PARA ESSE AUTUADO</span></md-checkbox></div><div layout=row layout-xs=column><md-checkbox class=\"m-b-30 md-primary\" ng-model=scope._autuado.importarResponsavel style=\"align-self: center\" ng-change=scope.importarResponsavel(scope._registro) ng-disabled=\"scope.autuadoBlock || scope._autuado.autuadoNaoIdentificado\" flex-gt-xs flex=70><span>IMPORTAR DADOS DO RESPONSÁVEL</span></md-checkbox></div><div layout=row layout-xs=column><md-checkbox class=\"m-b-30 md-primary\" ng-model=scope._autuado.autuadoNaoIdentificado style=\"align-self: center\" flex-gt-xs flex=70><span>O AUTUADO NÃO FOI IDENTIFICADO</span></md-checkbox></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button type=button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=scope.adicionarNovoAutuado(scope._autuado) ng-disabled=scope.autuadoBlock><span>ADICIONAR AUTUADO</span></md-button></div></div></div><div layout=row layout-align=\"center start\" class=m-t-20><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column><span>Status</span></th><th md-column><span>CPF/CNPJ</span></th><th md-column><span>Nome</span></th><th md-column><span>SEI</span></th><th md-column width=100px><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"autuado in scope._registro.autuados | orderBy: \'responsavel.nome\'\"><td md-cell>{{autuado.status}}</td><td md-cell>{{autuado.responsavel.cpfCnpj}}</td><td md-cell>{{autuado.responsavel.nome}}</td><td md-cell><span ng-show=autuado.processoSEI>{{autuado.processoSEI}} </span><span ng-show=!autuado.processoSEI>-</span></td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left ng-show=!autuado.id><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=scope.excluirRegistro(autuado)><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr><tr md-row ng-show=\"scope._registro.autuados.length === 0 || !scope._registro.autuados\"><td md-cell class=text-left colspan=100%>Nenhum autuado cadastrado!</td></tr></tbody></table></md-table-container></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-raised md-primary btn-form-action\" ui-sref=fiscalizacao.relatorio.listar ng-show=!scope.apenasLeitura><span>Cancelar</span></md-button><md-button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=scope.previousTab() ng-show=!scope.apenasLeitura><span>Voltar</span></md-button><md-button type=button class=\"md-raised md-primary btn-form-action m-l-15\" ng-show=!scope.apenasLeitura ng-click=scope.salvarTabCadastroAutuado(scope._registro)><span>Salvar</span></md-button><md-button type=button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=scope.nextTab() ng-show=\"!scope.apenasLeitura && scope._registro.autuados.length > 0\"><span>Continuar</span></md-button></div></div></div></ng-form></md-tab><md-tab ng-repeat=\"autuado in scope._registro.autuados | orderBy: \'responsavel.nome\'\" label={{autuado.responsavel.nome}} ng-show=\"scope._registro.autuados.length > 0\" ng-if=\"autuado.responsavel.cpfCnpj !== \'-\'\" ng-init=\"nomeForm = \'formQuestionarioAutuado\' + autuado.responsavel.cpfCnpj\"><ng-form name={{nomeForm}} novalidate isolate-form><bem-uso-comum ng-if=\"scope._registro.tipoImovel.tipo == \'BEM_USO_COMUM\' && scope._registro.ocorrenciaDeInfracao && scope._registro.autuados.length > 0\" relatorio-fiscalizacao=scope._registro autuado=autuado tipos-documento=scope._tiposDocumento apenas-leitura=scope.apenasLeitura></bem-uso-comum><bem-dominial ng-if=\"scope._registro.tipoImovel.tipo == \'DOMINIAL\' && scope._registro.ocorrenciaDeInfracao && scope._registro.autuados.length > 0\" relatorio-fiscalizacao=scope._registro autuado=autuado tipos-documento=scope._tiposDocumento apenas-leitura=scope.apenasLeitura></bem-dominial><imovel-funcional ng-if=\"scope._registro.tipoImovel.tipo == \'FUNCIONAL\' && scope._registro.ocorrenciaDeInfracao && scope._registro.autuados.length > 0\" relatorio-fiscalizacao=scope._registro autuado=autuado tipos-documento=scope._tiposDocumento apenas-leitura=scope.apenasLeitura></imovel-funcional><bem-uso-especial ng-if=\"scope._registro.tipoImovel.tipo == \'BENS_USO_ESPECIAL\'  && scope._registro.ocorrenciaDeInfracao && scope._registro.autuados.length > 0\" relatorio-fiscalizacao=scope._registro autuado=autuado tipos-documento=scope._tiposDocumento apenas-leitura=scope.apenasLeitura></bem-uso-especial><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-raised md-primary btn-form-action\" ui-sref=fiscalizacao.relatorio.listar><span>Cancelar</span></md-button><md-button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=scope.previousTab() ng-show=!scope.apenasLeitura><span>Voltar</span></md-button><md-button type=button class=\"md-raised md-primary btn-form-action m-l-15\" ng-if=!scope.apenasLeitura ng-click=\"scope.salvarTabQuestionarioAutuado(scope._registro, autuado.responsavel.cpfCnpj)\"><span>Salvar</span></md-button><md-button type=button class=\"md-raised md-primary btn-form-action m-l-15\" ng-click=scope.nextTab() ng-show=\"!scope.apenasLeitura && autuado.documentos.length > 0\"><span>Continuar</span></md-button></div></div></div></ng-form></md-tab><md-tab label=Concluir ng-if=scope.showConluirTab><div layout=row layout-xs=column class=m-t-20><div flex><h3>Concluir Autuação</h3></div></div><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head><tr md-row><th md-column class=text-left width=10%><span>Tipo</span></th><th md-column class=text-left width=10%><span>Status</span></th><th md-column class=text-left width=70%><span>Autuado</span></th><th md-column class=text-left width=10%><span>Ação</span></th></tr></thead><tbody md-body ng-repeat=\"documento in scope._documentos | orderBy: \'responsavel.nome\'\"><tr md-row><td md-cell class=text-left>{{scope._tiposDocumento[documento.tipoDocumento]}}</td><td md-cell class=text-left>{{scope._documentoStatus[documento.status]}}</td><td md-cell class=text-left>{{documento.responsavel.nome}}</td><td md-cell class=text-left><span ng-show=\"documento.statusDocumentoSei != \'AUTUADO\' && documento.status != \'AUTO_EXCLUIDO\'\"><md-button class=\"md-raised md-primary btn-form-action m-l-15\" title=\"{{scope._registro.ordemFiscalizacao.status == \'AGUARDANDO_REVALIDACAO\'? \'Essa ação não pode ser executada enquanto a Ordem de Fiscalização estiver aguardando revalidação\':\'\'}}\" ng-click=scope.autuarDocumento(documento.id) ng-disabled=\"documento.status != \'ENTREGUE\'\n                                                || scope._registro.ordemFiscalizacao.status == \'AGUARDANDO_REVALIDACAO\'\"><span>Autuar</span></md-button></span><span ng-show=\"documento.statusDocumentoSei == \'AUTUADO\'\"><md-button class=\"md-raised md-primary btn-form-action m-l-15\" disabled><span>Autuado</span></md-button></span></td></tr></tbody></table></md-table-container></div></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-raised md-primary btn-form-action\" ui-sref=fiscalizacao.relatorio.listar><span>Cancelar</span></md-button><md-button type=button class=\"md-raised md-primary btn-form-action m-l-15\" ng-if=!scope.apenasLeitura ng-click=scope.salvarFormRegistro(scope._registro)><span>Salvar</span></md-button><md-button type=button class=\"md-raised md-primary btn-form-action m-l-15\" title=\"{{\n                                   scope._registro.ordemFiscalizacao.status == \'AGUARDANDO_REVALIDACAO\'? \'Essa ação não pode ser executada enquanto a Ordem de Fiscalização estiver aguardando revalidação\':\'\'}}\" ng-disabled=\"scope._registro.ordemFiscalizacao.status == \'AGUARDANDO_REVALIDACAO\'\" ng-click=scope.validar(scope._registro) ng-if=!scope.documentoPendente><span>Concluir relatório</span></md-button></div></div></div></md-tab></md-tabs></form>");
$templateCache.put("scripts-fiscalizacao/pages/relatorio/detalhar/view/relatorio-modal.tmpl.html","<md-dialog style=\"max-width: 100% !important;  max-height: 100% !important;  width: 600px !important;  height: 400px !important\" id=modal><div><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Justificativa</span><md-button class=md-icon-button ng-click=scope.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><form ng-cloak name=formJustificativa ng-submit=scope.concluir(scope._registro) novalidate><md-dialog-content><div layout=row layout-xs=column style=\"padding: 20px\"><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa</label><textarea ng-model=scope._registro.justificativa maxlength=3000 md-maxlength=3000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura ng-required=true></textarea></md-input-container></div></md-dialog-content><md-dialog-actions layout=row><br><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=\"md-raised md-primary\" ng-click=scope.cancelModal()><span>Fechar</span></md-button></div><div layout=row layout-align=\"space-around center\"><md-button type=submit class=\"md-raised md-primary\"><span>Concluir</span></md-button></div></section></div></md-dialog-actions></form></div></md-dialog>");
$templateCache.put("scripts-fiscalizacao/pages/relatorio/documento/view/relatorio-documento.html","<md-dialog style=\"width: 1000px !important\"><md-toolbar class=m-b-20><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Formulário de Sanção</span><md-button class=md-icon-button ng-click=scope.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><form ng-cloak name=formDocumento ng-submit=scope.persistirRegistro(scope.documento) novalidate><md-dialog-content><div id=su-fiscalizacao><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100><label>Tipo de Documento</label><md-select ng-model=scope.documento.tipoDocumento ng-disabled=true required><md-option ng-value=k ng-repeat=\"(k,v) in scope._tiposDocumento track by $index\">{{v}}</md-option></md-select></md-input-container></div><div ng-show=scope.documento.tipoDocumento><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-show=\"scope.documento.tipoDocumento ==\'NOTIFICACAO\'\"><label>Tipo de Notificação</label><md-select ng-model=scope.documento.tipoNotificacao ng-disabled=true ng-required=\"scope.documento.tipoDocumento ==\'NOTIFICACAO\'\"><md-option ng-value=k ng-repeat=\"(k,v) in scope._tiposNotificacao track by $index\">{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column class=m-b-20><md-radio-group layout=row ng-model=scope.documento.uploadArquivo name=upload class=\"md-block m-b-20\" flex-gt-xs flex=100 style=\"align-self: center\" ng-disabled=scope.apenasLeitura ng-change=scope.changeUpload(scope.documento.uploadArquivo)><label class=p-r-25>Deseja realizar upload de arquivo?</label><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group><md-radio-group layout=row class=\"md-block m-b-20 fileUpload md-raised md-primary btn-form-action\" ng-if=\"scope.documento.uploadArquivo && !scope.documento.arquivo.id\" flex-gt-xs flex=100 style=\"height: 44px !important\"><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=scope.adicionarArquivoDocumento($files) ng-disabled=scope.apenasLeitura><label>Carregar Arquivo</label></md-button></md-radio-group><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-if=scope.documento.arquivo.id><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope.documento.arquivo.id}}><strong>{{scope.documento.arquivo.nome}}</strong></a></md-input-container></div><div ng-show=!scope.documento.uploadArquivo><div layout=row layout-xs=column class=m-b-40><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data da Autuação</label><md-datepicker-custom flex-gt-xs flex=100 ng-model=scope.documento.dataAutuacao md-min-date=scope.minDataFiscalizacao name=dataAutuacao class=md-block flex-gt-xs md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=false ng-disabled=scope.apenasLeitura></md-datepicker-custom><div class=\"md-block validation-messages\" flex-gt-xs flex=100| style=\"color:red; font-size: 12px\"><div ng-show=formDocumento.dataAutuacao.$error.mindate>Escolha uma data maior que a data início da fiscalização</div></div></div><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Hora da Autuacao</label><input input-horario type=text ng-model=scope.documento.horaAutuacao class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento != \'NOTIFICACAO\'\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100><label>{{scope.documento.tipoDocumento == \'AUTO_DE_EMBARGO\' ? \'Área de Embargo (m²)\' : \'Área da Infração\'}}</label><input ui-number-mask type=text ng-model=scope.documento.areaEmbargo class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>CNPJ/CPF do responsável</label><input ui-br-cpfcnpj-mask type=text ng-model=scope.documento.responsavel.cpfCnpj class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nome do responsável</label><input type=text ng-model=scope.documento.responsavel.nome maxlength=100 ng-maxlength=100 class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Documento de Identificação</label><md-select ng-model=scope.documento.documentoIdentificacao ng-disabled=scope.apenasLeitura ng-change=scope.mudaDocumentoIdentificacao(documento.documentoIdentificacao)><md-option ng-value=k ng-repeat=\"(k,v) in scope._documentosIdentificacao track by $index\">{{ v }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50 ng-show=\"scope.documento.documentoIdentificacao != \'PASSAPORTE\'\"><label>Número do Documento</label><input type=text ui-number-mask=0 ng-model=scope.documento.numeroDocumento class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50 ng-show=\"scope.documento.documentoIdentificacao == \'PASSAPORTE\'\"><label>Número do Documento</label><input type=text ng-model=scope.documento.numeroDocumento class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.documentoIdentificacao == \'PASSAPORTE\'\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100><label>País Emissor</label><input type=text ng-model=scope.documento.paisEmissor maxlength=150 ng-maxlength=150 class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.documentoIdentificacao == \'RG\'\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Orgão Emissor</label><input type=text ng-model=scope.documento.orgaoEmissor maxlength=150 ng-maxlength=150 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label>UF de Emissão</label><md-select ng-model=scope.documento.ufEmissao ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._estados\">{{ item.sigla }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Filiação</label><input type=text ng-model=scope.documento.filiacao maxlength=200 ng-maxlength=200 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data de Nascimento</label><md-datepicker-custom flex-gt-xs flex=100 name=dataNascimento ng-model=scope.documento.dataNascimento class=md-input flex-gt-xs md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true ng-disabled=scope.apenasLeitura></md-datepicker-custom></div></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nacionalidade</label><input type=text ng-model=scope.documento.nacionalidade maxlength=100 ng-maxlength=100 class=md-input ng-required=false ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Naturalidade</label><input type=text ng-model=scope.documento.naturalidade maxlength=100 ng-maxlength=100 class=md-input ng-required=false ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column class=m-b-20><md-radio-group layout=row ng-model=scope.documento.confirmaEndereco name=confirmaEndereco ng-change=scope.documentoEnderecoConfirmado() class=md-block flex-gt-xs flex=100 ng-disabled=scope.apenasLeitura><label class=p-r-25>Endereço do Imóvel é o mesmo para correspondência?</label><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div><div ng-show=!scope.documento.confirmaEndereco><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>UF</label><input type=text ng-model=scope.documento.endereco.estado class=md-input ng-required=!scope.documento.confirmaEndereco disabled></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>CEP</label><input type=text ng-model=scope.documento.endereco.cep ng-blur=scope.getCEPDocumento(scope.documento.endereco.cep) ui-br-cep-mask class=md-input ng-required=!scope.documento.confirmaEndereco ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label>Município</label><md-select ng-model=scope.documento.endereco.municipio ng-disabled=\"scope.apenasLeitura || scope.desabilitaMunicipio\" ng-required=!scope.documento.confirmaEndereco><md-option ng-value=item ng-repeat=\"item in scope._municipiosDocumento\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Destinação</label><md-select ng-model=scope.documento.endereco.tipoImovel ng-required=!scope.documento.confirmaEndereco ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._tiposImovel\">{{ item.nome }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label>Tipo de logradouro</label><md-select ng-model=scope.documento.endereco.tipoLogradouro ng-required=!scope.documento.confirmaEndereco ng-disabled=scope.apenasLeitura><md-option ng-value=k ng-repeat=\"(k,y) in scope._tipoLogradouro\">{{y}}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Bairro/Distrito</label><input type=text ng-model=scope.documento.endereco.bairro class=md-input ng-required=!scope.documento.confirmaEndereco maxlength=100 ng-maxlength=100 ng-disabled=\"scope.apenasLeitura  || scope.desabilitaBairro\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nº</label><input type=text ng-model=scope.documento.endereco.numero class=md-input ng-required=!scope.documento.confirmaEndereco ng-maxlength=100 maxlength=1000 ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Logradouro</label><input type=text ng-model=scope.documento.endereco.logradouro class=md-input ng-required=!scope.documento.confirmaEndereco ng-disabled=\"scope.apenasLeitura  || scope.desabilitaLogradouro\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Natureza do imóvel</label><md-select ng-model=scope.documento.endereco.zoneamento ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._zoneamentos\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Complemento</label><input type=text ng-model=scope.documento.endereco.complemento class=md-input ng-required=!scope.documento.confirmaEndereco maxlength=200 ng-maxlength=200 ng-disabled=scope.apenasLeitura></md-input-container></div></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Telefone para Contato</label><input type=text ui-br-phone-number=\"\" ng-model=scope.documento.telefoneContato class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Email para Contato</label><input type=text ng-model=scope.documento.emailContato class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><label>Descrição do Fato</label><textarea ng-model=scope.documento.descricaoFato style=\"opacity: 0.6\" maxlength=1000 md-maxlength=1000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura></textarea></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.justificativaEdicao && !scope.documento.justificativaExclusao\"><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa da Edição</label><textarea ng-model=scope.documento.justificativaEdicao style=\"opacity: 0.6\" maxlength=1000 md-maxlength=1000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura></textarea></md-input-container></div><div layout=row layout-xs=column ng-show=scope.documento.justificativaExclusao><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa da Exclusão</label><textarea ng-model=scope.documento.justificativaExclusao style=\"opacity: 0.6\" maxlength=1000 md-maxlength=1000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura></textarea></md-input-container></div><div ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><div layout=row layout-xs=column><h3 class=m-t-20>Sanções Administrativas</h3></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><h4 class=m-t-20>Selecione as sanções aplicadas</h4></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.embargo ng-disabled=true>Embargo da obra serviço ou atividade;</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.demolicao ng-disabled=true>Demolição e/ou remoção de aterro, construção, obra, cercas, ou demais benfeitorias, bem os equipamentos instalados, à conta de quem os houver efetuado, caso não sejam passíveis de regularização.</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.multa ng-disabled=true>Multa mensal</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=scope.documento.sancaoAdministrativa.multa><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Área (m²)</label><input type=text ui-number-mask ng-model=scope.documento.sancaoAdministrativa.areaMulta ng-change=scope.calcularMulta() class=md-input ng-disabled=true ng-required=scope.documento.sancaoAdministrativa.multa></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Valor definido para m²:</label><input type=text ui-money-mask=2 ng-model=scope.documento.sancaoAdministrativa.valorMulta.valorMulta ng-change=scope.calcularMulta() disabled class=md-input ng-required=scope.documento.sancaoAdministrativa.multa></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Valor total da Multa</label><input type=text ui-money-mask=2 ng-model=scope.documento.sancaoAdministrativa.totalMulta class=md-input ng-disabled=true ng-required=scope.documento.sancaoAdministrativa.multa></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.desocupacao ng-disabled=true>Desocupação de imóvel com pagamento de indenização</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=scope.documento.sancaoAdministrativa.desocupacao><div flex-gt-xs flex=100><div layout=row layout-xs=column><h4 class=m-t-20>Período de Ocupação</h4></div><div layout=row layout-xs=column ng-show=scope.documento.sancaoAdministrativa.desocupacao><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano inicial</label><input ng-required=scope.documento.sancaoAdministrativa.desocupacao ui-hide-group-sep ui-number-mask=0 type=text ng-keyup=scope.validaTamanhoAno() ng-blur=scope.validaAnoInicial(documento.sancaoAdministrativa.anoInicialOcupacao) maxlength=4 ng-model=scope.documento.sancaoAdministrativa.anoInicialOcupacao ng-maxlength=4 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano final</label><input ng-required=scope.documento.sancaoAdministrativa.desocupacao ui-hide-group-sep type=text ui-number-mask=0 ng-keyup=scope.validaTamanhoAno() ng-blur=scope.validaAnoFinal(documento.sancaoAdministrativa.anoFinalOcupacao) maxlength=4 ng-model=scope.documento.sancaoAdministrativa.anoFinalOcupacao ng-maxlength=4 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Área do terreno (m²)</label><input ng-required=scope.documento.sancaoAdministrativa.desocupacao type=text ui-number-mask ng-model=scope.documento.sancaoAdministrativa.areaOcupacao class=md-input ng-disabled=scope.apenasLeitura ng-change=scope.calcularIndenizacaoArea(scope.documento.sancaoAdministrativa.multas)></md-input-container></div><div layout=row layout-xs=column><h4 class=m-t-20>Período de Cobrança</h4></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano inicial</label><input type=text ui-number-mask=0 ui-hide-group-sep maxlength=4 ng-maxlength=4 ng-blur=scope.validaIntervaloCobranca() ng-model=scope.documento.sancaoAdministrativa.anoInicialCobranca class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano final</label><input type=text ui-number-mask=0 ui-hide-group-sep maxlength=4 ng-maxlength=4 ng-change=scope.validaIntervaloCobranca() ng-model=scope.documento.sancaoAdministrativa.anoFinalCobranca class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33></md-input-container></div><div layout=row layout-xs=column ng-repeat=\"multa in scope.documento.sancaoAdministrativa.multas\" style=\"padding-left: 30px\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Planta de Valores Genéricos para {{multa.ano}} (R$/m²)</label><input type=text ui-money-mask=2 ng-change=scope.calculaVdp(multa) ng-model=multa.plantaValoresGenericos class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>VDP</label><input type=text ui-money-mask=2 ng-model=multa.valorDominioPleno class=md-input disabled ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Indenização em {{multa.ano}}</label><input type=text ui-money-mask=2 ng-model=multa.indenizacao class=md-input disabled></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=75></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=25 style=\"text-align: right\"><label style=\"text-align: right; margin-left: 53px\">Valor total da Indenização</label><span><strong>{{scope.documento.sancaoAdministrativa.valorTotalIndenizacao | currency: \"R$ \": 2}}</strong></span></md-input-container></div></div></div></div></div></div></div></md-dialog-content><md-dialog-actions layout=row><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=\"md-raised md-primary m-r-0 m-t-0\" ng-click=scope.cancelModal() ng-disabled=scope.realizandoUpload>Fechar</md-button><md-button type=submit class=\"md-raised md-primary btn-form-action m-r-0\" ng-show=!scope.apenasLeitura>Confirmar</md-button></div></section></div></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-fiscalizacao/pages/relatorio/exportar-relatorio/view/relatorio-exportar.tmpl.html","<form name=formRegistro ng-submit=scope.exportarCsv(scope._filtros) novalidate><div layout=row layout-xs=column><div flex><h2>Exportação de Dados das Fiscalizações</h2></div></div><div layout=row layout-xs=column><div flex><h3>Filtros de Pesquisa</h3></div></div><div layout=row layout-xs=column><div class=md-block flex-gt-xs flex=100 style=\"margin-top: -15px\"><span class=datepicker-label><span>Período</span></span></div></div><div layout=row layout-xs=column><md-datepicker-custom name=dataInicio ng-model=scope._filtros.dataInicio class=md-block flex=25 flex-xs=100 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true ng-disabled=scope.apenasLeitura required></md-datepicker-custom><md-datepicker-custom name=dataFim ng-model=scope._filtros.dataFim class=md-block flex=25 flex-xs=100 md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true ng-disabled=scope.apenasLeitura required></md-datepicker-custom><md-input-container class=md-block flex=25 flex-xs=100><label><span>Estados</span></label><md-select ng-model=scope._filtros.uf multiple required ng-disabled=scope.apenasLeitura><md-button class=layout-fill ng-click=scope.selecionaTodosEstados()>Todos</md-button><md-option ng-value=item.sigla ng-repeat=\"item in scope._estados\">{{item.descricao}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><div flex><a ng-model=collapsed ng-click=\"collapsed=!collapsed\" class=link-exportar><h4 style=\"color: #4CAF50\">Opções de exportação<md-icon style=\"color: #4CAF50\" ng-bind=\"\'add\'\"><md-tooltip>Abrir opções de exportação</md-tooltip></md-icon></h4></a></div></div><div ng-show=collapsed style=\"border-top: 2px solid #4caf50\"><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosCadastrador()>Dados do Cadastrador<md-tooltip>Marcar/desmarcar todas opções de Dados do Cadastrador</md-tooltip></a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.dataHora>Data e Hora</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.cpfCadastrador>CPF cadastrador</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nomeCadastrador>Nome cadastrador</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosDemanda()><md-tooltip>Marcar/desmarcar todas opções de Dados da Demanda</md-tooltip>Dados da Demanda</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nuProcessoDemanda>Número do Processo</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.protocoloOrdemFiscalizacao>Ordem de Fiscalização</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.dataFiscalizacao>Data da Fiscalização</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.motivacaoDemanda>Motivação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.cnpjCpfResponsavelDemanda>CNPJ/CPF do responsável</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nomeResponsavelDemanda>Nome do responsável</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.ufDemanda>UF</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.areaImovelDemanda>Área do imóvel (m²)</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.cepDemanda>CEP</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.destinacaoDemanda>Destinação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.municipioDemanda>Município</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.bairroDemanda>Bairro/Distrito</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.tipoLogradouroDemanda>Tipo Logradouro</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.logradouroDemanda>Logradouro</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.numeroEnderecoDemanda>Nº</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.complementoDemanda>Complemento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.naturezaImovelDemanda>Natureza do imóvel</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosRelatorio()><md-tooltip>Marcar/desmarcar todas opções de Dados do Relatório</md-tooltip>Dados do Relatório</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.benfeitoriasRelatorio>Benfeitorias encontradas</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.confirmaDadosOcupanteRelatorio>Confirma dados do ocupantes</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.cnpjCpfResponsavelRelatorio>CNPJ/CPF do responsável</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nomeResponsavelRelatorio>Nome do responsável</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.confirmaEnderecoRelatorio>Confirma o endereco?</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.ufRelatorio>UF</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.areaImovelRelatorio>Área do imóvel (m²)</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.cepRelatorio>CEP</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.municipioRelatorio>Município</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.bairroRelatorio>Bairro/Distrito</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.tipoLogradouroRelatorio>Tipo de Logradouro</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.logradouroRelatorio>Logradouro</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.numeroEnderecoRelatorio>Nº</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.complementoRelatorio>Complemento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.naturezaImovelRelatorio>Natureza do imóvel</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.descritivoRelatorio>Descritivo Geral</md-checkbox></md-input-container><md-input-container class=md-block flex=30 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.equipamentoRPARelatorio>Utilizou equipamento RPA na ação?</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosCroqui()><md-tooltip>Marcar/desmarcar todas opções de Extraído do CROQUI</md-tooltip>Extraído do CROQUI</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.latitudeCroqui>Latitude</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.longitudeCroqui>Longitude</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.encaminhamentosCroqui>Encaminhamentos</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.membrosEquipeCroqui>Membros da Equipe</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.infracaoCroqui>Ocorreu Infração?</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosAutuado()><md-tooltip>Marcar/desmarcar todas opções de Cadastro do Autuado</md-tooltip>Cadastro do Autuado</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.cpfCnpjAutuado>CPF/CNPJ</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nomeAutuado>Nome</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.processosSEIAutuado>Processos SEI</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosAutoEmbargo()><md-tooltip>Marcar/desmarcar todas opções de Auto de Embargo</md-tooltip>Auto de Embargo</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.uploadArquivoAutoEmbargo>Deseja realizar upload de arquivo</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.dataAutuacaoAutoEmbargo>Data de autuação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.horaAutuacaoAutoEmbargo>Hora de autuação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.areaEmbargoAutoEmbargo>Área de Embargo (m²)</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.cnpjCpfResponsavelAutoEmbargo>CNPJ/CPF do responsável</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nomeResponsavelAutoEmbargo>Nome responsável</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.documentoIdResponsavelAutoEmbargo>Documento de identificação</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosRG()><md-tooltip>Marcar/desmarcar todas opções de RG</md-tooltip>RG</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.numeroRGAutoEmbargo>Número do documento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.orgaoEmissorRGAutoEmbargo>Órgão emissor</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.ufRGAutoEmbargo>UF de emissão</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosPassaporte()><md-tooltip>Marcar/desmarcar todas opções de Passaporte</md-tooltip>Passaporte</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.numeroPassaporteAutoEmbargo>Número do documento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.paisEmissorPassaporteAutoEmbargo>País emissor</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.filiacaoPassaporteAutoEmbargo>Filiação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.dataNascimentoPassaporteAutoEmbargo>Data de nascimento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nacionalidadePassaporteAutoEmbargo>Nacionalidade</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.naturalidadePassaporteAutoEmbargo>Naturalidade</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=30 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.correspondenciaDiferentePassaporteAutoEmbargo>Endereço do imóvel é o mesmo para correspondência</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.telefonePassaporteAutoEmbargo>Telefone para contato</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.emailPassaporteAutoEmbargo>Email para contato</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.descricaoFatoPassaporteAutoEmbargo>Descrição do fato</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosAutoInfracao()><md-tooltip>Marcar/desmarcar todas opções de Auto de Infracao</md-tooltip>Auto de infração</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.uploadArquivoAutoInfracao>Deseja realizar upload de arquivo?</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.dataAutuacaoAutoInfracao>Data de autuação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.horaAutuacaoAutoInfracao>Hora de autuação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.areaEmbargoAutoInfracao>Área de embargo (m²)</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.cnpjCpfResponsavelAutoInfracao>CNPJ/CPF do responsável</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nomeResponsavelAutoInfracao>Nome do responsável</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.rgResponsavelAutoInfracao>Documento de indentificação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.numeroRgResponsavelAutoInfracao>Número do documento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.orgaoEmissorRgResponsavelAutoInfracao>Órgão emissor</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.ufEmissaoRgResponsavelAutoInfracao>UF de emissão</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.passaporteAutoInfracao>Passaporte</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.numeroPassaporteAutoInfracao>Número do documento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.paisEmissorPassaporteAutoInfracao>País emissor</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.filiacaoPassaporteAutoInfracao>Filiação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.dataNascimentoPassaporteAutoInfracao>Data de nascimento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nacionalidadePassaporteAutoInfracao>Nacionalidade</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.naturalidadePassaporteAutoInfracao>Naturalidade</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=30 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.correspondenciaDiferentePassaporteAutoInfracao>Endereço do imóvel é o mesmo para correspondência</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.telefonePassaporteAutoInfracao>Telefone para contato</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.emailPassaporteAutoInfracao>Email para contato</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.descricaoFatoPassaporteAutoInfracao>Descrição do fato</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosSancao()><md-tooltip>Marcar/desmarcar todas opções de Sanções aplicadas</md-tooltip>Sanções aplicadas</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=30 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.embargoObraSancao>Embargo da obra, serviço ou atividade</md-checkbox></md-input-container><md-input-container class=md-block flex=70 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.demolicaoSancao>Demolição e/ou remoção de aterro, construção, obra, cercas, ou demais benfeitorias, bem os equipamentos instalados, à conta de quem os houver efetuado, caso não seja passíveis de regularização.</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.multaSancao>Multa mensal</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.areaSancao>Área (m²)</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.valorMetroSancao>Valor definido para m²</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.valorTotalMultaSancao>Valor total da multa</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=40 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.indenizacaoSancao>Desocupação do imóvel com pagamento de indenização</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosPeriodoOcupacaoSancao()><md-tooltip>Marcar/desmarcar todas opções de Período de ocupação</md-tooltip>Período de ocupação</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.anoInicialOcupacaoSancao>Ano inicial</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.anoFinalOcupacaoSancao>Ano final</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.areaOcupacaoSancao>Área do terreno (m²)</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosPeriodoCobrancaSancao()><md-tooltip>Marcar/desmarcar todas opções de Período de cobrança</md-tooltip>Período de cobrança</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.anoInicialCobrancaSancao>Ano inicial</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.anoFinalCobrancaSancao>Ano final</md-checkbox></md-input-container></div><div layout=row layout-xs=column><div flex=100><a class=link-exportar ng-click=scope.habilitaTodosDadosNotificacao()><md-tooltip>Marcar/desmarcar todas opções de Notificação</md-tooltip>Notificação</a></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.tipoNotificacao>Tipo de notificação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.uploadArquivoNotificacao>Deseja realizar upload de arquivo?</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.dataAutuacaoNotificacao>Data de autuação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.horaAutuacaoNotificacao>Hora de autuação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.areaEmbargoNotificacao>Área de embargo (m²)</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.cnpjCpfResponsavelNotificacao>CNPJ/CPF do responsável</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nomeResponsavelNotificacao>Nome do responsável</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.rgResponsavelNotificacao>Documento de indentificação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.numeroRgResponsavelNotificacao>Número do documento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.orgaoEmissorRgResponsavelNotificacao>Órgão emissor</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.ufEmissaoRgResponsavelNotificacao>UF de emissão</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.passaporteNotificacao>Passaporte</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.numeroPassaporteNotificacao>Número do documento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.paisEmissorPassaporteNotificacao>País emissor</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.filiacaoPassaporteNotificacao>Filiação</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.dataNascimentoPassaporteNotificacao>Data de nascimento</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.nacionalidadePassaporteNotificacao>Nacionalidade</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.naturalidadePassaporteNotificacao>Naturalidade</md-checkbox></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex=30 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.correspondenciaDiferentePassaporteNotificacao>Endereço do imóvel é o mesmo para correspondência</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.telefonePassaporteNotificacao>Telefone para contato</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.emailPassaporteNotificacao>Email para contato</md-checkbox></md-input-container><md-input-container class=md-block flex=20 flex-xs=100><md-checkbox class=md-primary ng-model=scope._filtros.descricaoFatoPassaporteNotificacao>Descrição do fato</md-checkbox></md-input-container></div></div><div layout=row layout-xs=column class=\"buttons-action no-border\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=scope.iniciarFiltros()><span>Redefinir</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=submit><span>Exportar dados em csv</span></md-button></div></div></div></form>");
$templateCache.put("scripts-fiscalizacao/pages/relatorio/justificativa-documento/view/justificativa-documento.html","<md-dialog style=\"max-width: 100%; max-height: 100%; width: 700px; height: 400px\" id=modal><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Justificativa para {{scope.tipo}}</span><md-button class=md-icon-button ng-click=scope.cancelModalJustificativaDocumento()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><form ng-cloak name=formJustificativaDocumento ng-submit=scope.salvar(scope.justificativa) novalidate><md-dialog-content><div layout=row layout-xs=column style=\"padding: 20px\" ng-if=scope.isEdicao><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa</label><textarea ng-model=scope.justificativa maxlength=3000 md-maxlength=3000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura ng-required=true></textarea></md-input-container></div><div layout=row layout-xs=column style=\"padding: 20px\" ng-if=!scope.isEdicao><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa</label><textarea ng-model=scope.justificativa maxlength=3000 md-maxlength=3000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura ng-required=true></textarea></md-input-container></div></md-dialog-content><md-dialog-actions layout=row><br><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=\"md-raised md-primary\" ng-click=scope.cancelModalJustificativaDocumento()><span>Fechar</span></md-button></div><div layout=row layout-align=\"space-around center\"><md-button type=submit class=\"md-raised md-primary\"><span>Salvar</span></md-button></div></section></div></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-fiscalizacao/pages/relatorio/listar/view/relatorio-listar.tmpl.html","<form name=scope.formRegistro novalidate><div layout=row layout-xs=column><div flex><h2>Consultar Relatório de Fiscalização</h2></div></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=33><label>Ordem Fiscalização</label><md-autocomplete class=md-autocomplete-icon class=md-autocomplete-icon md-floating-label=\"\" md-no-cache=true id=search md-search-text-change=scope.getDescricaoBySearch() md-search-text=scope.filtros.descricao md-selected-item-change=scope.getDemandasByProtocolo(item) md-item-text=scope.filtros.descricao md-items=\"item in scope.descricoes\" md-min-length=2><md-item-template><span md-highlight-text=scope.filtros.descricao md-highlight-flags=^i>{{item}}</span></md-item-template></md-autocomplete></md-input-container><md-input-container class=md-block flex-gt-xs flex=33><label>CPF do Fiscal</label><input name=\">CPF do Fiscal\" ng-model=scope.filtros.responsavel.cpfCnpj></md-input-container><md-input-container class=md-block flex-gt-xs flex=33><label>Demanda</label><md-select ng-model=scope.filtros.demanda ng-disabled=!scope._demandas.length><md-option ng-value=item ng-repeat=\"item in scope._demandas\">{{ item.id | numeroDemanda}}</md-option></md-select></md-input-container><md-input-container class=md-block flex-gt-xs flex=33><label><span>Status</span></label><md-select ng-model=scope.filtros.status><md-optgroup><md-option ng-if=\"k !== \'NAO_POSSUI\'\" ng-value=k ng-repeat=\"(k,v) in scope._status track by $index\">{{v}}</md-option></md-optgroup></md-select></md-input-container></div><div layout=row layout-xs=column class=buttons-action><div class=md-block flex-gt-xs flex=50><md-button class=\"md-primary md-raised\" ui-sref=fiscalizacao.relatorio.incluir><md-icon md-font-set=material-icons>add</md-icon><span>Novo Relatório</span></md-button></div><div class=md-block flex-gt-xs flex=50><div layout=row layout-align=end><md-button class=\"md-primary md-raised\" type=button ng-click=scope.limparFiltros()><md-icon md-font-set=material-icons>filter_list</md-icon><span>Limpar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=button ng-click=scope.filtrar()><md-icon md-font-set=material-icons>search</md-icon><span>Pesquisar</span></md-button></div></div></div></form><div layout=row layout-align=\"center start\"><div flex><md-table-container><table md-table><thead md-head md-order=scope.filtros.order md-on-reorder=scope.filtrar><tr md-row><th md-column md-order-by=ordemFiscalizacao.protocolo class=text-center width=300px><span>Ordem Fiscalização</span></th><th md-column md-order-by=ordemFiscalizacao.protocolo class=text-center width=300px><span>Última atualização</span></th><th md-column md-order-by=demanda class=text-center><span>Demanda</span></th><th md-column md-order-by=status><span>Status</span></th><th md-column width=100px><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"item in scope._registros\"><td md-cell class=text-center>{{item.ordemFiscalizacao.protocolo}}</td><td md-cell class=text-center>{{item.dataEdicao | date : \'dd/MM/yyyy\'}}</td><td md-cell class=text-center>{{item.demanda.id | numeroDemanda}}</td><td md-cell>{{scope._status[item.status]}}</td><td md-cell><div class=container-acoes><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-click=scope.excluirRegistro(item) ng-show=\"item.status !== \'CANCELADO\' && item.status !== \'REJEITADO\' && item.status !== \'VALIDADA\' && item.status !== \'NAO_EXECUTADA\'\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.relatorio.alterar({id : item.id})\" ng-show=\"item.status !== \'CANCELADO\' && item.status !== \'REJEITADO\' && item.status !== \'CONCLUIDO\' && item.status !== \'VALIDADA\'&& item.status !== \'NAO_EXECUTADA\'\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.relatorio.validar({id : item.id})\" ng-show=\"item.status !== \'CANCELADO\' && item.status !== \'REJEITADO\' && item.status !== \'EM_CONSTRUCAO\' && item.status !== \'VALIDADA\' && item.status !== \'NAO_EXECUTADA\'\"><md-icon ng-bind=\"\'check-circle\'\"><md-tooltip>Validar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ui-sref=\"fiscalizacao.relatorio.consultar({id : item.id})\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Visualizar</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr><tr md-row ng-show=\"!scope._registros.length > 0\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhum resultado encontrado!</td></tr></tbody></table></md-table-container><md-table-pagination md-on-paginate=scope.filtrar ng-show=\"scope._registros.length > 0\" md-limit=scope.tamanhoLimite md-limit-options=scope.optionTamanho md-page=scope.registroInicial md-total={{scope.totalItems}} md-page-select></md-table-pagination></div></div>");
$templateCache.put("scripts-fiscalizacao/pages/relatorio/nova-demanda/view/justificativa-nova-demanda-modal.tmpl.html","<md-dialog style=\"max-width: 100%; max-height: 100%; width: 700px; height: 400px\" id=modal><md-toolbar><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Justificativa para adicionar demanda à O.F vinculada</span><md-button class=md-icon-button ng-click=scope.cancelModalNovaDemanda()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><form ng-cloak name=formJustificativaNovaDemanda ng-submit=scope.salvar(scope.justificativaNovaDemanda) novalidate><md-dialog-content><div layout=row layout-xs=column style=\"padding: 20px\"><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa</label><textarea ng-model=scope.justificativaNovaDemanda maxlength=3000 md-maxlength=3000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura ng-required=true></textarea><div style=\"margin-top: 25px; font-weight: bold\"><span>*A justificativa será envida para o precesso da demanda no SEI após validação de Relatório</span></div></md-input-container></div></md-dialog-content><md-dialog-actions layout=row><br><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=\"md-raised md-primary\" ng-click=scope.cancelModalNovaDemanda()><span>Fechar</span></md-button></div><div layout=row layout-align=\"space-around center\"><md-button type=submit class=\"md-raised md-primary\"><span>Novo cadastro de demanda</span></md-button></div></section></div></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-fiscalizacao/app/directives/relatorio-infracao/bem-dominial/view/bem-dominial.html","<div><h3 class=\"m-b-20 m-t-20\">Ocorrências da infração (BEM DOMINIAL)</h3><div><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaUm ng-change=bemUsoDominialPerguntaUm(autuado.ocorrenciaInfracao.perguntaUm) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=isBemDominial><label class=\"p-r-25 full-width\">1) Há duvidas sobre a autoria da infração ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaUm === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação </strong>para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaUm != null && (autuado.ocorrenciaInfracao.perguntaUm === true || autuado.ocorrenciaInfracao.perguntaUm === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDois ng-change=bemUsoDominialPerguntaDois(autuado.ocorrenciaInfracao.perguntaDois) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=true><label class=\"p-r-25 full-width\">2) Há duvidas sobre os limites do imóvel fiscalizado ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaDois === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaDois != null && (autuado.ocorrenciaInfracao.perguntaDois === true || autuado.ocorrenciaInfracao.perguntaDois === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaTres ng-change=bemUsoDominialPerguntaTres(autuado.ocorrenciaInfracao.perguntaTres) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=true><label class=\"p-r-25 full-width\">3) O imóvel fiscalizado se encontra em terreno de marinha ou em terreno marginal sem LPM/LMEO homologada ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaTres === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaTres === false\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaQuatro ng-change=bemUsoDominialPerguntaQuatro(autuado.ocorrenciaInfracao.perguntaQuatro) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaTres === false\"><label class=\"p-r-25 full-width\">4) Foi encontrado obras em andamento sem autorização ou destinação definida pela da União?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatro === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se solicitar <strong>Demolição e/ou Remoção</strong> para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização.</p><p>Deve-se aplicar <strong>Multa</strong>por metro quadrado de benfeitoria/construção encontrada por mês de ocorrência de infração com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art. 6.º do Decreto-Lei 2.398/87, no art. 10 da Lei 9.636/98 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar o <strong>Auto de Embargo</strong> para paralisação dos serviços, com base no art. 6.º do Decreto-Lei 2.398/87 e arts. 12 e 13 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><div layout=row layout-xs=column class=m-t-15><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=100><label><span>Área da Obra (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaObra ui-number-mask=2 type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-required=\"autuado.ocorrenciaInfracao.perguntaQuatro === true\"></md-input-container></div></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatro != null && (autuado.ocorrenciaInfracao.perguntaQuatro === true || autuado.ocorrenciaInfracao.perguntaQuatro === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaCinco ng-change=bemUsoDominialPerguntaCinco(autuado.ocorrenciaInfracao.perguntaCinco) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaQuatro === true\"><label class=\"p-r-25 full-width\">5) Foi encontrado ocupante não reconhecido e com Construções promovidas sem a autorização/destinação Concedida pela União em imóvel fiscalizado ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaCinco === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar <strong>Demolição e/ou Remoção</strong> para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização.</p><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de cercamento encontrado por mês de ocorrência da infração com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art. 6.º do Decreto-Lei 2.398/87, no art. 10 da Lei 9.636/98 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União”.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área da Obra (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaObra ui-number-mask=2 type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-required=\"autuado.ocorrenciaInfracao.perguntaCinco === true\"></md-input-container></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaCinco != null && (autuado.ocorrenciaInfracao.perguntaCinco === true || autuado.ocorrenciaInfracao.perguntaCinco === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaSeis ng-change=bemUsoDominialPerguntaSeis(autuado.ocorrenciaInfracao.perguntaSeis) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaCinco === false\"><label class=\"p-r-25 full-width\">6) Foi instalado equipamentos sem autorização da União ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaSeis === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar <strong></strong>Demolição e/ou Remoção para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização.</p><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de benfeitorias encontradas por mês de ocorrência de infração com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art. 6.º do Decreto-Lei 2.938/87, no art. 10 da Lei 9.363/98 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área do Equipamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaEquipamento ui-number-mask=2 type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-required=\"autuado.ocorrenciaInfracao.perguntaSeis === true\"></md-input-container></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaSeis != null && (autuado.ocorrenciaInfracao.perguntaSeis === true || autuado.ocorrenciaInfracao.perguntaSeis === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaSete ng-change=bemUsoDominialPerguntaSete(autuado.ocorrenciaInfracao.perguntaSete) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSeis != null && (autuado.ocorrenciaInfracao.perguntaSeis === true || autuado.ocorrenciaInfracao.perguntaSeis === false)\"><label class=\"p-r-25 full-width\">7) Outras benfeitorias foram realizadas causando descaracterização do imóvel, Sem autorização da União ? (por exemplo, plantações ou Outras benfeitorias que privatizem a área ?)</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaSete === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar<strong>Multa</strong> por metro quadrado de área desmatada; com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art. 6.º do Decreto-Lei 2.938/87, no art. 10 da Lei 9.363/98 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área de Outras Benfeitorias (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaOutrasBenfeitorias ui-number-mask=2 type=text maxlength=80 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSete === true\"></md-input-container></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaSete != null && (autuado.ocorrenciaInfracao.perguntaSete === true || autuado.ocorrenciaInfracao.perguntaSete === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaOito ng-change=bemUsoDominialPerguntaOito(autuado.ocorrenciaInfracao.perguntaOito) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSete != null && (autuado.ocorrenciaInfracao.perguntaSete === true || autuado.ocorrenciaInfracao.perguntaSete === false)\"><label class=\"p-r-25 full-width\">8) Tem comprovação por meio do órgão ambiental Sobre a ocorrência de desmatamento ou supressão de vegetação sem autorização da União e do órgão ambiental ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaOito === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de benfeitoria/construção encontrada por mês de ocorrência de infração com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área de Desmatamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaDesmatamento ui-number-mask=2 type=text maxlength=80 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOito === true\"></md-input-container></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaOito != null && (autuado.ocorrenciaInfracao.perguntaOito === true || autuado.ocorrenciaInfracao.perguntaOito === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaNove ng-change=bemUsoDominialPerguntaNove(autuado.ocorrenciaInfracao.perguntaNove) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOito != null && (autuado.ocorrenciaInfracao.perguntaOito === true || autuado.ocorrenciaInfracao.perguntaOito === false)\"><label class=\"p-r-25 full-width\">9) Foi encontrado ocupante não reconhecido e com cercamento promovido sem a autorização/destinação concedida pela União em imóvel fiscalizado ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaNove === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar <strong>Demolição e/ou Remoção</strong> para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização.</p><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de cercamento encontrado por mês de ocorrência de infração com base no art. 6.º do Decreto-Lei 2.398/87 art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art. 6.º do Decreto-lei 2.938/87, no art. 10 da Lei 9.636/98 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área de Cercamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaCercamento ui-number-mask=2 type=text maxlength=80 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaNove === true\"></md-input-container></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaNove != null && (autuado.ocorrenciaInfracao.perguntaNove === true || autuado.ocorrenciaInfracao.perguntaNove === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDez ng-change=bemUsoDominialPerguntaDez(autuado.ocorrenciaInfracao.perguntaDez) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaNove != null && (autuado.ocorrenciaInfracao.perguntaNove === true || autuado.ocorrenciaInfracao.perguntaNove === false)\"><label class=\"p-r-25 full-width\">10) O ocupante atual é regularizado pela SPU, porém, está Inadimplente com as taxas contratuais ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaDez === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong>para a desocupação do imóvel em 30 dias com base nos arts. 1.º e 11 da Lei 9636/98 e nos arts. 132, 88 e 89 § 2.º \"a\" do Decreto-Lei 9760/46 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaDez === false\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaOnze ng-change=bemUsoDominialPerguntaOnze(autuado.ocorrenciaInfracao.perguntaOnze) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDez === false\"><label class=\"p-r-25 full-width\">11) O ocupante atual é regularizado pela SPU, foi comprovado a ocorrência de crime ambiental na área, conforme manifestação do órgão ambiental responsável ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaOnze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong> para a desocupação imediata do imóvel com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaOnze != null && (autuado.ocorrenciaInfracao.perguntaOnze === true || autuado.ocorrenciaInfracao.perguntaOnze === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDoze ng-change=bemUsoDominialPerguntaDoze(autuado.ocorrenciaInfracao.perguntaDoze) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOnze != null && (autuado.ocorrenciaInfracao.perguntaOnze === true || autuado.ocorrenciaInfracao.perguntaOnze === false)\"><label class=\"p-r-25 full-width\">12) O ocupante atual é regularizado pela SPU e foi detectado crime contra o patrimônio (parcelamento irregular de solo sem autorização da União, demolição de benfeitoria da União sem autorização, dentre outros) ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaDoze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong>para a desocupação imediata do imóvel com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaDoze === false\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaTreze ng-change=bemUsoDominialPerguntaTreze(autuado.ocorrenciaInfracao.perguntaTreze) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDoze === false\"><label class=\"p-r-25 full-width\">13) O ocupante atual é regularizado pela SPU com instrumento precário e o imóvel foi solicitado para uso em serviço público ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaTreze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deverá ser aplicada uma <strong>Notificação</strong> para a desocupação do imóvel em 90 dias, se urbano, ou em 180 dias, se rural, com base no arts. 132 e 89 § 3.º “a” e \"b\" do Decreto-lei 9760/46, nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaTreze === true\"><md-radio-group layout=row class=md-block ng-model=autuado.ocorrenciaInfracao.tipoNotificacaoSelected ng-change=handleTipoNotificacaoSelected(autuado.ocorrenciaInfracao.tipoNotificacaoSelected) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaTreze === true\"><md-radio-button ng-value=true class=md-primary>Notificação para desocupação em 90 dias</md-radio-button><md-radio-button ng-value=false class=md-primary>Notificação para desocupação em 180 dias</md-radio-button></md-radio-group></div></div><div layout=row layout-xs=column ng-show=autuado.ocorrenciaInfracao.areaAutuada><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=25><label><span>Área Autuada (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaAutuada ui-number-mask=2 type=text maxlength=80 ng-disabled=true></md-input-container></div><div layout=row layout-xs=column class=\"m-t-15 infracao-titulo\"><div flex><h3 class=\"no-margin m-t-10\">Infrações</h3></div></div><div layout=row layout-xs=column class=\"m-b-10 m-t-10\"><md-table-container class=md-block flex-gt-xs flex=100><table md-table><thead md-head><tr md-row><th md-column class=text-left><span>Nome do Documento</span></th><th md-column width=100px class=text-center><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in autuado.documentos\" ng-show=\"autuado.documentos.length > 0\"><td md-cell class=text-left><span ng-show=\"(documento.status != \'DOCUMENTO_EXCLUIDO\') || (!documento.status)\">{{tiposDocumento[documento.tipoDocumento]}}</span> <span ng-show=\"documento.status == \'DOCUMENTO_EXCLUIDO\'\">{{tiposDocumento[documento.tipoDocumento]}} - Excluído - Justificativa: <span class=text-bold>{{documento.justificativaExclusao}}</span></span></td><td md-cell class=text-right><div class=\"container-acoes full-width\"><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-href=/fiscalizacao/api/documentos/download/{{documento.id}} target=_blank ng-show=documento.id><md-icon ng-bind=\"\'picture_as_pdf\'\"><md-tooltip>Imprimir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"excluirDocumento(documento, $index, $event)\" ng-show=\"documento.status != \'AUTO_EXCLUIDO\' && documento.id\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"updateDocumentoDominial(documento, $index, $event)\" ng-show=\"documento.status != \'AUTO_EXCLUIDO\'\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"visualizarDocumento(documento, $event)\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Visualizar</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr><tr md-row ng-show=\"!autuado.documentos.length > 0 || scope.showMensagem === true\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhum documento registrado!</td></tr></tbody></table></md-table-container></div></div>");
$templateCache.put("scripts-fiscalizacao/app/directives/relatorio-infracao/bem-uso-comum/view/bem-uso-comum.html","<div><h3 class=\"m-b-20 m-t-20\">Ocorrências da infração (BEM DE USO COMUM)</h3><div><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaUm ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaUm,\'Um\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=isBemUsoComum><label class=\"p-r-25 full-width\">1) Há dúvidas sobre a autoria da infração?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaUm === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaUm === false \"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDois ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaDois,\'Dois\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaUm === false\"><label class=\"p-r-25 full-width\">2) Há duvidas sobre os limites do imóvel fiscalizado?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaDois === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaDois === false \"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaTres ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaTres,\'Tres\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDois === false\"><label class=\"p-r-25 full-width\"><span>3) Trata-se de infraestrutura náutica/atividade portuária?</span></label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaTres === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União e na Portaria nº 11.190, de 1 de novembro de 2018.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaTres === false \"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaQuatro ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaQuatro,\'Quatro\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaTres === false\"><label class=\"p-r-25 full-width\">4) Foram encontradas irregularidades na praia e sua gestão foi outorgada ao Município?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatro === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação</strong> ao Município para providências quanto ao achado, com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatro === false \"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaCinco ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaCinco,\'Cinco\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaQuatro === false\"><label class=\"p-r-25 full-width\">5) Foi encontrado estabelecimento operando serviços na área de uso comum do povo em desacordo com a Destinação concedida pela União?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaCinco === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar o <strong>Auto de Embargo</strong> para paralisação dos serviços, com base no art. 6.º do Decreto-Lei 2.398/87 e arts. 12 e 13 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Ainda deve-se aplicar a <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaCinco)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaSeis ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaSeis,\'Seis\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaCinco === false\"><label class=\"p-r-25 full-width\">6) Foi encontrado estabelecimento operando serviços na área de uso comum do povo sem autorização ou destinação definida pela União ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaSeis === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se solicitar Demolição e/ou Remoção para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização</p><p>Deve-se aplicar o Auto de Embargo para paralisação dos serviços, com base no art. 6.º do Decreto-Lei 2.398/87 e arts. 12 e 13 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União</p><div layout=row layout-xs=column class=m-t-15><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=100 ng-show=\"autuado.ocorrenciaInfracao.perguntaSeis === true\"><label><span>Área de Construção (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaConstruida ui-number-mask=2 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOito !== null && autuado.ocorrenciaInfracao.perguntaOito === true\"></md-input-container></div></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaSeis)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaSete ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaSete,\'Sete\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSeis === false || autuado.ocorrenciaInfracao.perguntaSeis === true\"><label class=\"p-r-25 full-width\">7) Foi encontrado obras em andamento na área de uso comum do povo sem autorização ou destinação definida pela da União ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaSete === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar Demolição e/ou Remoção para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização</p><p>Deve-se aplicar uma Multa por metro quadrado de área construída com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União</p><p>Deve-se aplicar um Auto de Embargo para paralisação dos serviços, com base no art. 6.º do Decreto-Lei 2.398/87 e arts. 12 e 13 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66 ng-show=\"autuado.ocorrenciaInfracao.perguntaSete === true\"><label><span>Área de Construção (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaConstrucao ui-number-mask=2 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSete === true\"></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaSete)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaOito ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaOito,\'Oito\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSete === false\"><label class=\"p-r-25 full-width\">8) Foi encontrada construção concluída na área de uso comum do povo sem autorização da União?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaOito === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar Demolição e/ou Remoção para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização</p><p>Deve-se aplicar uma Multa por metro quadrado de área construída com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66 ng-show=\"autuado.ocorrenciaInfracao.perguntaOito === true\"><label><span>Área da obra (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaObra ui-number-mask=2 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOito !== null && autuado.ocorrenciaInfracao.perguntaOito === true\"></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaOito)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaNove ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaNove,\'Nove\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"(autuado.ocorrenciaInfracao.perguntaOito === true || autuado.ocorrenciaInfracao.perguntaOito === false) || autuado.ocorrenciaInfracao.perguntaSete === true\"><label class=\"p-r-25 full-width\">9) Foi encontrado cercamento de área de uso comum do povo sem autorização da União ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaNove === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar Demolição e/ou Remoção para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização</p><p>Deve-se aplicar Multa por metro quadrado de benfeitoria/construção encontrada por mês de ocorrência de infração com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66 ng-show=\"autuado.ocorrenciaInfracao.perguntaNove === true\"><label><span>Área de Cercamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaCercamento ui-number-mask=2 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaNove === true\"></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaNove)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDez ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaDez,\'Dez\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaNove === false || autuado.ocorrenciInfracao.perguntaNove === true\"><label class=\"p-r-25 full-width\">10) Foi encontrado aterro em espelho d’água sem autorização da União ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaDez === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar Demolição e/ou Remoção para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização</p><p>Deve-se aplicar uma Multa por metro quadrado de área construída com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66 ng-show=\"autuado.ocorrenciaInfracao.perguntaDez === true\"><label><span>Área de Aterro (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaAterro ui-number-mask=2 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDez === true\"></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaDez)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaOnze ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaOnze,\'Onze\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDez === false || autuado.ocorrenciaInfracao.perguntaDez === true\"><label class=\"p-r-25 full-width\">11) Foi instalado equipamentos em área de uso comum do povo sem autorização da União ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaOnze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar Demolição e/ou Remoção para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização.</p><p>Deve-se aplicar uma Multa por metro quadrado de área construída com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66 ng-show=\"autuado.ocorrenciaInfracao.perguntaOnze === true\"><label><span>Área de Equipamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaEquipamento ui-number-mask=2 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOnze === true\"></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaOnze)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDoze ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaDoze,\'Doze\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOnze === false || autuado.ocorrenciaInfracao.perguntaOnze === true\"><label class=\"p-r-25 full-width\">12) Tem comprovação por meio do órgão ambiental sobre a ocorrência de desmatamento ou supressão de mangue sem autorização da União e do órgão ambiental ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaDoze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar uma Multa por metro quadrado de área construída com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66 ng-show=\"autuado.ocorrenciaInfracao.perguntaDoze === true\"><label><span>Área de Desmatamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaDesmatamento ui-number-mask=2 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDoze === true\"></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaDoze)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaTreze ng-change=\"changePergunta(autuado.ocorrenciaInfracao.perguntaTreze,\'Treze\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDoze === false || autuado.ocorrenciaInfracao.perguntaDoze === true\"><label class=\"p-r-25 full-width\">13) Outras benfeitorias foram realizadas na área de uso Comum do povo, causando descaracterização do imóvel, sem autorização da União? (por exemplo, plantações ou Outras benfeitorias que privatizem a área)</label><md-radio-button ng-value=true class=\"md-primary radio-button-custon\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custon\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaTreze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar uma Multa por metro quadrado de área construída com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66 ng-show=\"autuado.ocorrenciaInfracao.perguntaTreze === true\"><label><span>Área de Outras Benfeitorias (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaOutrasBenfeitorias ui-number-mask=2 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=\"autuado.ocorrenciaInfracao.perguntaTreze === true\"></md-input-container></md-input-container></div></div><div layout=row layout-xs=column ng-show=autuado.ocorrenciaInfracao.areaAutuada><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=25><label><span>Área Autuada (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaAutuada ui-number-mask=2 type=text maxlength=80 ng-disabled=true></md-input-container></div><div layout=row layout-xs=column class=\"m-t-15 infracao-titulo\"><div flex><h3 class=\"no-margin m-t-10\">Infrações</h3></div></div><div layout=row layout-xs=column class=\"m-b-10 m-t-10\"><md-table-container class=md-block flex-gt-xs flex=100><table md-table><thead md-head><tr md-row><th md-column class=text-left><span>Nome do Documento</span></th><th md-column width=100px class=text-center><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in autuado.documentos\" ng-show=\"autuado.documentos.length > 0\"><td md-cell class=text-left><span ng-show=\"(documento.status != \'AUTO_EXCLUIDO\') || (!documento.status)\">{{tiposDocumento[documento.tipoDocumento]}}</span> <span ng-show=\"documento.status == \'AUTO_EXCLUIDO\'\">{{tiposDocumento[documento.tipoDocumento]}} - Excluído - Justificativa: <span class=text-bold>{{documento.justificativaExclusao}}</span></span></td><td md-cell class=text-right><div class=\"container-acoes full-width\"><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-href=/fiscalizacao/api/documentos/download/{{documento.id}} target=_blank ng-show=documento.id><md-icon ng-bind=\"\'picture_as_pdf\'\"><md-tooltip>Imprimir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"excluirDocumento(documento, $index, $event)\" ng-show=\"documento.status != \'AUTO_EXCLUIDO\' && documento.id\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"updateDocumento(documento, $index, $event)\" ng-show=\"documento.status != \'AUTO_EXCLUIDO\'\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"visualizarDocumento(documento, $event)\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Visualizar</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr><tr md-row ng-show=\"!autuado.documentos.length > 0 || scope.showMensagem === true\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhum documento registrado!</td></tr></tbody></table></md-table-container></div></div>");
$templateCache.put("scripts-fiscalizacao/app/directives/relatorio-infracao/bem-uso-especial/view/bem-uso-especial.html","<div><h3 class=\"m-b-20 m-t-20\">Ocorrências da infração (BEM DE USO ESPECIAL)</h3><div><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaUm ng-change=\"bemUsoEspecialPergunta(autuado.ocorrenciaInfracao.perguntaUm, \'Um\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=isBemEspecial><label class=\"p-r-25 full-width\">1) Há dúvidas sobre a autoria da infração ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaUm === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>No prazo máximo de 30(trinta) dias, contados do recebimento do presente, oferecer manifestação, apresentar informações ou documentos voltados ao esclarecimento da autoria e/ou à apresentação de elementos que componham a materialidade da possível infração ao patrimônio da União, de modo que se esclareçam os fatos sob indicação e/ou a relação de Vossa Senhoria com os mesmos.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaUm === false\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDois ng-change=\"bemUsoEspecialPergunta(autuado.ocorrenciaInfracao.perguntaDois, \'Dois\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaUm === false\"><label class=\"p-r-25 full-width\">2) Na vistoria realizada, foi encontrado um terceiro privado utilizando o imóvel ao invés do órgão público que se destinou o imóvel ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div></div><!-- Fluxo do bem de usu especial --><div ng-show=\"autuado.ocorrenciaInfracao.perguntaDois === false\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaTres ng-change=\"bemUsoEspecialPergunta(autuado.ocorrenciaInfracao.perguntaTres, \'Tres\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDois === false\"><label class=\"p-r-25 full-width\">3) O contrato de destinação ao Órgão se encontra vigente ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaTres === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e art. 18 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaTres === false\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaQuatro ng-change=\"bemUsoEspecialPergunta(autuado.ocorrenciaInfracao.perguntaQuatro, \'Quatro\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaTres === false && autuado.ocorrenciaInfracao.perguntaDois === false\"><label class=\"p-r-25 full-width\">4) Foi apurado que a finalidade do contrato não está sendo seguida ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatro === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e art. 18 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatro === false\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaCinco ng-change=\"bemUsoEspecialPergunta(autuado.ocorrenciaInfracao.perguntaCinco, \'Cinco\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"(autuado.ocorrenciaInfracao.perguntaQuatro === false || autuado.ocorrenciaInfracao.perguntaQuatro === true) && autuado.ocorrenciaInfracao.perguntaDois === false\"><label class=\"p-r-25 full-width\">5) Foi apurado que os encargos previstos no contrato estão sendo descumpridos?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaCinco === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75>No prazo máximo de 30(trinta) dias, contados do recebimento do presente, oferecer manifestação, apresentar informações ou documentos voltados ao esclarecimento da autoria e/ou à apresentação de elementos que componham a materialidade da possível infração ao patrimônio da União, de modo que se esclareçam os fatos sob indicação e/ou a relação de Vossa Senhoria com os mesmos.</md-input-container></div></div></div><!-- </> Fluxo do bem de usu especial --><!-- Fluxo do bem dominial --><div ng-show=\"autuado.ocorrenciaInfracao.perguntaDois === true\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaTres ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaTres, \'Tres\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=isBemUsoDominialObrigatorio(autuado.ocorrenciaInfracao.perguntaDois)><label class=\"p-r-25 full-width\">3) Há duvidas sobre os limites do imóvel fiscalizado ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaTres === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos Arts. 1º e 11º da Lei 9636/98 e Arts. 18º e 22º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p></md-input-container></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaTres)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaQuatro ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaQuatro, \'Quatro\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=isBemUsoDominialObrigatorio(autuado.ocorrenciaInfracao.perguntaTres)><label class=\"p-r-25 full-width\">4) O imóvel fiscalizado se encontra em terreno de marinha ou em terreno marginal sem LPM/LMEO homologada ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatro === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos Arts. 1º e 11º da Lei 9636/98 e Arts. 18º e 22º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaQuatro)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaCinco ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaCinco, \'Cinco\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=isBemUsoDominialObrigatorio(autuado.ocorrenciaInfracao.perguntaQuatro)><label class=\"p-r-25 full-width\">5) Foi encontrado obras em andamento sem autorização ou destinação definida pela da União?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaCinco === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de benfeitoria/construção encontrada por mês de ocorrência de infração com base no art 6º do Decreto-Lei 2.398/87 e Arts. 6º ao 10º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art 6º do Decreto-Lei 2.398/87, no art 10º da Lei 9.636/98 e arts 6º ao 10º da Instrução NormativaNº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p><p>Deve-se aplicar o <strong>Auto de Embargo</strong> para paralisação dos serviços, com base no art. 6º do Decreto-Lei 2.398/87 e arts. 5º ao 10º da Instrução Normativa Nº 1 de 23 de janeiro de 2017, da Secretaia de parimônio da União.</p><div layout=row layout-xs=column class=m-t-15><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=100><label><span>Área da Obra (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaObra ui-number-mask=2 type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-required=isBemUsoDominialObrigatorio(autuado.ocorrenciaInfracao.perguntaCinco)></md-input-container></div></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaCinco)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaSeis ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaSeis, \'Seis\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSeis != null && (autuado.ocorrenciaInfracao.perguntaSeis === true || autuado.ocorrenciaInfracao.perguntaSeis === false) && autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">6) Foi encontrado ocupante não reconhecido e com Construções promovidas sem a autorização/destinação Concedida pela União em imóvel fiscalizado ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaSeis === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar <strong>Notificação</strong> para em até 30 dias provar a sua regularidade e promover a sua regularização com base no art 6º do Decreto-Lei 2.398/87 e Arts. 6º ao 10º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de cercamento encontrado pro mês de ocorrência da infração com base no art 6º do Decreto-Lei 2.398/87 e Arts. 6º ao 10º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no Art. 6º do Decreto-Lei 2.398/87, no Art. 10º a Lei 9.636/98 e Arts. 6º ao 10º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66 ng-show=\"autuado.ocorrenciaInfracao.perguntaSeis === true\"><label><span>Área da Obra (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaObra ui-number-mask=2 type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-required=isBemUsoDominialObrigatorio(autuado.ocorrenciaInfracao.perguntaSeis)></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaSeis)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaSete ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaSete, \'Sete\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSete != null && (autuado.ocorrenciaInfracao.perguntaSete === true || autuado.ocorrenciaInfracao.perguntaSete === false) && autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">7) Foi instalado equipamentos sem autorização da União ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaSete === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de benfeitorias encontradas por mês de ocorrência de infração com base no art 6º do Decreto-Lei 2.398/87 e Arts. 6º ao 10º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no Art. 6º do Decreto-Lei 2.938/87, no Art. 10º da Lei 9.363/98 e Arts. 6º ao 10º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área do Equipamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaEquipamento ui-number-mask=2 type=text maxlength=80 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-required=isBemUsoDominialObrigatorio(autuado.ocorrenciaInfracao.perguntaSete)></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaSete)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaOito ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaOito, \'Oito\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOito != null && (autuado.ocorrenciaInfracao.perguntaOito === true || autuado.ocorrenciaInfracao.perguntaOito === false) && autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">8) Outras benfeitorias foram realizadas causando descaracterização do imóvel, Sem autorização da União ? (por exemplo, plantações ou Outras benfeitorias que privatizem a área ?)</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaOito === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de área desmatada; com base no art 6º do Decreto-Lei 2.398/87 e Arts. 6º ao 10º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área de Outras Benfeitorias (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaOutrasBenfeitorias ui-number-mask=2 type=text maxlength=80 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=isBemUsoDominialObrigatorio(autuado.ocorrenciaInfracao.perguntaOito)></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaOito)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaNove ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaNove, \'Nove\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaNove != null && (autuado.ocorrenciaInfracao.perguntaNove === true || autuado.ocorrenciaInfracao.perguntaNove === false) && autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">9) Tem comprovação por meio do órgão ambiental Sobre a ocorrência de desmatamento ou supressão de vegetação sem autorização da União e do órgão ambiental ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaNove === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar <strong>Auto de Infração </strong>contendo:<br>1 - Multa por metro quadrado de benfeitoria/construção encontrada por mês de ocorrência de infração.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área de Desmatamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaDesmatamento ui-number-mask=2 type=text maxlength=80 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=isBemUsoDominialObrigatorio(autuado.ocorrenciaInfracao.perguntaNove)></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaNove)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDez ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaDez, \'Dez\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDez != null && (autuado.ocorrenciaInfracao.perguntaDez === true || autuado.ocorrenciaInfracao.perguntaDez === false) && autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">10) Foi encontrado ocupante não reconhecido e com cercamento promovido sem a autorização/destinação concedida pela União em imóvel fiscalizado ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaDez === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de cercamento encontrado por mês de ocorrência de infração com base no art 6º do Decreto-Lei 2.398/87 e Arts. 6º ao 10º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União vom base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no Art. 6º do Decreto-lei 2.938/87, no Art. 10º da Lei 9.636/98 e Arts. 6º ao 10º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área de Cercamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaCercamento ui-number-mask=2 type=text maxlength=80 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" ng-required=isBemUsoDominialObrigatorio(autuado.ocorrenciaInfracao.perguntaDez)></md-input-container></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaDez)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaOnze ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaOnze, \'Onze\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOnze != null && (autuado.ocorrenciaInfracao.perguntaOnze === true || autuado.ocorrenciaInfracao.perguntaOnze === false) && autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">11) O ocupante atual é regularizado pela SPU, porém, está Inadimplente com as taxas contratuais ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaOnze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong> para a desocupação do imóvel em 30 dias com base nos Arts. 1º e 11º da Lei 9636/98 e nos Arts. 132 e 88, 89 §2º \"a\" do Decreto-Lei 9760/46, e Art. 18º e 22º da Instrução Normativa Nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaOnze)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDoze ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaDoze, \'Doze\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDoze === false && autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">12) O ocupante atual é regularizado pela SPU, foi comprovado a ocorrência de crime ambiental na área, conforme manifestação do órgão ambiental responsável ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaDoze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong> para a desocupação imediata do imóvel com base nos arts. 1 e 11 da Lei 9636/98 e Art. 18º e 22º da Instrução Normativa nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaDoze)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaTreze ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaTreze, \'Treze\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaTreze != null && (autuado.ocorrenciaInfracao.perguntaTreze === true || autuado.ocorrenciaInfracao.perguntaTreze === false) && autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">13) O ocupante atual é regularizado pela SPU e foi detectado crime contra o patrimônio (parcelamento irregular de solo sem autorização da União, demolição de benfeitoria da União sem autorização, dentre outros) ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaTreze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong> para a desocupação imediata do imóvel com base nos arts. 1 e 11 da Lei 9636/98 e Art. 18º e 22º da Instrução Normativa nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p></md-input-container></div></div><div ng-show=isPerguntaRespondida(autuado.ocorrenciaInfracao.perguntaTreze)><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaQuatorze ng-change=\"bemUsoDominialPergunta(autuado.ocorrenciaInfracao.perguntaQuatorze, \'Quatorze\')\" flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaQuatorze === false && autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">14) O ocupante atual é regularizado pela SPU com instrumento precário e o imóvel foi solicitado para uso em serviço público ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatorze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deverá ser aplicada uma <strong>Notificação</strong> para a desocupação do imóvel em 90 dias, se urbano, ou em 180 dias, se rural, com base no arts. 132 e 89 §3º “a” e \"b\" do Decreto-lei 9760/46, nos arts. 1 e 11 da Lei 9636/98 e Art. 18º e 22º da Instrução Normativa nº 1, de 23 de janeiro de 2017, da Secretaria do Patrimônio da União.</p></md-input-container></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatorze === true\"><md-radio-group layout=row class=md-block ng-model=autuado.ocorrenciaInfracao.tipoNotificacaoSelected ng-change=handleTipoNotificacaoSelected(autuado.ocorrenciaInfracao.tipoNotificacaoSelected) flex-gt-xs flex=75 ng-disabled=\"(apenasLeitura) || (autuado.ocorrenciaInfracao.concluido)\" style=\"height: 20px;margin-top: 10px\" ng-required=isBemUsoDominialObrigatorio(autuado.ocorrenciaInfracao.perguntaQuatorze)><md-radio-button ng-value=true class=md-primary>Notificação para desocupação em 90 dias</md-radio-button><md-radio-button ng-value=false class=md-primary>Notificação para desocupação em 180 dias</md-radio-button></md-radio-group></div></div></div><!-- </> Fluxo do bem dominial --><div layout=row layout-xs=column class=\"m-t-15 infracao-titulo\"><div flex><h3 class=\"no-margin m-t-10\">Infrações</h3></div></div><div layout=row layout-xs=column class=\"m-b-10 m-t-10\"><md-table-container class=md-block flex-gt-xs flex=100><table md-table><thead md-head><tr md-row><th md-column class=text-left><span>Nome do Documento</span></th><th md-column width=100px class=text-center><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in autuado.documentos\" ng-show=\"autuado.documentos.length > 0\"><td md-cell class=text-left><span ng-show=\"(documento.status != \'DOCUMENTO_EXCLUIDO\') || (!documento.status)\">{{tiposDocumento[documento.tipoDocumento]}}</span> <span ng-show=\"documento.status == \'DOCUMENTO_EXCLUIDO\'\">{{tiposDocumento[documento.tipoDocumento]}} - Excluído - Justificativa: <span class=text-bold>{{documento.justificativaExclusao}}</span></span></td><td md-cell class=text-right><div class=\"container-acoes full-width\"><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-href=/fiscalizacao/api/documentos/download/{{documento.id}} target=_blank ng-show=documento.id><md-icon ng-bind=\"\'picture_as_pdf\'\"><md-tooltip>Imprimir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"excluirDocumento(documento, $index, $event)\" ng-show=\"documento.status != \'AUTO_EXCLUIDO\' && documento.id\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"updateDocumento(documento, $index, $event)\" ng-show=\"documento.status != \'AUTO_EXCLUIDO\'\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"visualizarDocumento(documento, $event)\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Visualizar</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr><tr md-row ng-show=\"!autuado.documentos.length > 0 || scope.showMensagem === true\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhum documento registrado!</td></tr></tbody></table></md-table-container></div></div>");
$templateCache.put("scripts-fiscalizacao/app/directives/relatorio-infracao/imovel-funcional/view/imovel-funcional.html","<div><h3 class=\"m-b-20 m-t-20\">Ocorrências da infração (Imóvel Funcional)</h3><div><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaUm ng-change=imovelFuncionalPerguntaUm(autuado.ocorrenciaInfracao.perguntaUm) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=true><label class=\"p-r-25 full-width\">1) Foi detectado que o ocupante da extinta permissão está no imóvel em um prazo superior a 30 dias ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaUm === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar Notificação para desocupação imediata e cobrança de multa especifica (10 vezes o valor da taxa de uso a cada 30 dias de ocupação irregular) do imóvel conforme art. 16 § 4° do Decreto 980, de 11 de novembro de 1993 e art. 15da Lei 8025 de 12 de abril de 1990.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaUm != null && (autuado.ocorrenciaInfracao.perguntaUm === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=\"autuado.ocorrenciaInfracao.perguntaDois = true\" ng-change=imovelFuncionalPerguntaDois(autuado.ocorrenciaInfracao.perguntaDois) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaUm === false\"><label class=\"p-r-25 full-width\">2) Na vistoria realizada, foi encontrado um terceiro privado utilizando o imóvel ao invés do permissionário ?</label><md-radio-button ng-value=true ng-disabled=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false ng-disabled=true style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaDois != null && (autuado.ocorrenciaInfracao.perguntaDois === true) && autuado.ocorrenciaInfracao.perguntaUm != null && (autuado.ocorrenciaInfracao.perguntaUm === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaTres ng-change=bemUsoDominialPerguntaTres(autuado.ocorrenciaInfracao.perguntaTres) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">3) Há duvidas sobre a autoria da infração ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaTres === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação </strong>para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaTres != null && (autuado.ocorrenciaInfracao.perguntaTres === true || autuado.ocorrenciaInfracao.perguntaTres === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaQuatro ng-change=bemUsoDominialPerguntaQuatro(autuado.ocorrenciaInfracao.perguntaQuatro) flex-gt-xs flex=75 ng-disabled=(apenasLeitu) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">4) Há duvidas sobre os limites do imóvel fiscalizado ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatro === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatro != null && (autuado.ocorrenciaInfracao.perguntaQuatro === true || autuado.ocorrenciaInfracao.perguntaQuatro === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaCinco ng-change=bemUsoDominialPerguntaCinco(autuado.ocorrenciaInfracao.perguntaCinco) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDois === true\"><label class=\"p-r-25 full-width\">5) O imóvel fiscalizado se encontra em terreno de marinha ou em terreno marginal sem LPM/LMEO homologada ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaCinco === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar a <strong>Notificação</strong> para apresentação de documentos comprovatórios no prazo de 30 dias, com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaCinco === false\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaSeis ng-change=bemUsoDominialPerguntaSeis(autuado.ocorrenciaInfracao.perguntaSeis) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSeis === false\"><label class=\"p-r-25 full-width\">6) Foi encontrado obras em andamento sem autorização ou destinação definida pela da União?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaSeis === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se solicitar <strong>Demolição e/ou Remoção</strong> para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização.</p><p>Deve-se aplicar <strong>Multa</strong>por metro quadrado de benfeitoria/construção encontrada por mês de ocorrência de infração com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art. 6.º do Decreto-Lei 2.398/87, no art. 10 da Lei 9.636/98 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar o <strong>Auto de Embargo</strong> para paralisação dos serviços, com base no art. 6.º do Decreto-Lei 2.398/87 e arts. 12 e 13 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><div layout=row layout-xs=column class=m-t-15><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=100><label><span>Área da Obra (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaObra ui-number-mask=2 type=text maxlength=80 ng-disabled=(apenasLeitura) ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-required=\"autuado.ocorrenciaInfracao.perguntaSeis === true\"></md-input-container></div></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaSeis != null && (autuado.ocorrenciaInfracao.perguntaSeis === true || autuado.ocorrenciaInfracao.perguntaSeis === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaSete ng-change=bemUsoDominialPerguntaSete(autuado.ocorrenciaInfracao.perguntaSete) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSeis === true\"><label class=\"p-r-25 full-width\">7) Foi encontrado ocupante não reconhecido e com Construções promovidas sem a autorização/destinação Concedida pela União em imóvel fiscalizado ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaSete === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar <strong>Demolição e/ou Remoção</strong> para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização.</p><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de cercamento encontrado por mês de ocorrência da infração com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art. 6.º do Decreto-Lei 2.398/87, no art. 10 da Lei 9.636/98 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União”.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área da Obra (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaObra ui-number-mask=2 type=text maxlength=80 ng-disabled=(apenasLeitura) ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-required=\"autuado.ocorrenciaInfracao.perguntaSete === true\"></md-input-container></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaSete != null && (autuado.ocorrenciaInfracao.perguntaSete === true || autuado.ocorrenciaInfracao.perguntaSete === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaOito ng-change=bemUsoDominialPerguntaOito(autuado.ocorrenciaInfracao.perguntaOito) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaSete === false\"><label class=\"p-r-25 full-width\">8) Foi instalado equipamentos sem autorização da União ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaOito === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar <strong></strong>Demolição e/ou Remoção para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização.</p><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de benfeitorias encontradas por mês de ocorrência de infração com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art. 6.º do Decreto-Lei 2.938/87, no art. 10 da Lei 9.363/98 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área do Equipamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaEquipamento ui-number-mask=2 type=text maxlength=80 ng-disabled=(apenasLeitura) ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-required=\"autuado.ocorrenciaInfracao.perguntaOito === true\"></md-input-container></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaOito != null && (autuado.ocorrenciaInfracao.perguntaOito === true || autuado.ocorrenciaInfracao.perguntaOito === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaNove ng-change=bemUsoDominialPerguntaNove(autuado.ocorrenciaInfracao.perguntaNove) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOito != null && (autuado.ocorrenciaInfracao.perguntaOito === true || autuado.ocorrenciaInfracao.perguntaOito === false)\"><label class=\"p-r-25 full-width\">9) Outras benfeitorias foram realizadas causando descaracterização do imóvel, Sem autorização da União ? (por exemplo, plantações ou Outras benfeitorias que privatizem a área ?)</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaNove === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar<strong>Multa</strong> por metro quadrado de área desmatada; com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art. 6.º do Decreto-Lei 2.938/87, no art. 10 da Lei 9.363/98 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área de Outras Benfeitorias (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaOutrasBenfeitorias ui-number-mask=2 type=text maxlength=80 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-disabled=(apenasLeitura) ng-required=\"autuado.ocorrenciaInfracao.perguntaNove === true\"></md-input-container></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaNove != null && (autuado.ocorrenciaInfracao.perguntaNove === true || autuado.ocorrenciaInfracao.perguntaNove === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDez ng-change=bemUsoDominialPerguntaDez(autuado.ocorrenciaInfracao.perguntaDez) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaNove != null && (autuado.ocorrenciaInfracao.perguntaNove === true || autuado.ocorrenciaInfracao.perguntaNove === false)\"><label class=\"p-r-25 full-width\">10) Tem comprovação por meio do órgão ambiental Sobre a ocorrência de desmatamento ou supressão de vegetação sem autorização da União e do órgão ambiental ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaDez === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de benfeitoria/construção encontrada por mês de ocorrência de infração com base no art. 6.º do Decreto-Lei 2.398/87 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área de Desmatamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaDesmatamento ui-number-mask=2 type=text maxlength=80 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-disabled=(apenasLeitura) ng-required=\"autuado.ocorrenciaInfracao.perguntaDez === true\"></md-input-container></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaDez != null && (autuado.ocorrenciaInfracao.perguntaDez === true || autuado.ocorrenciaInfracao.perguntaDez === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaOnze ng-change=bemUsoDominialPerguntaOnze(autuado.ocorrenciaInfracao.perguntaOnze) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDez != null && (autuado.ocorrenciaInfracao.perguntaDez === true || autuado.ocorrenciaInfracao.perguntaDez === false)\"><label class=\"p-r-25 full-width\">11) Foi encontrado ocupante não reconhecido e com cercamento promovido sem a autorização/destinação concedida pela União em imóvel fiscalizado ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaOnze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><div><p>Deve-se solicitar <strong>Demolição e/ou Remoção</strong> para demolir ou remover as obras em até 30 dias ou para provar a regularidade da obra e promover a sua regularização.</p><p>Deve-se aplicar <strong>Multa</strong> por metro quadrado de cercamento encontrado por mês de ocorrência de infração com base no art. 6.º do Decreto-Lei 2.398/87 art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p><p>Deve-se aplicar <strong>Indenização</strong> por uso irregular de imóvel da União com base no metro quadrado do terreno ocupado irregularmente por ano ou fração de ano de ocupação; com base no art. 6.º do Decreto-lei 2.938/87, no art. 10 da Lei 9.636/98 e art. 11 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></div><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=66><label><span>Área de Cercamento (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaCercamento ui-number-mask=2 type=text maxlength=80 ng-change=calculaAreaAutuada(autuado.ocorrenciaInfracao) ng-disabled=(apenasLeitura) ng-required=\"autuado.ocorrenciaInfracao.perguntaOnze === true\"></md-input-container></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaOnze != null && (autuado.ocorrenciaInfracao.perguntaOnze === true || autuado.ocorrenciaInfracao.perguntaOnze === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaDoze ng-change=bemUsoDominialPerguntaDoze(autuado.ocorrenciaInfracao.perguntaDoze) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaOnze != null && (autuado.ocorrenciaInfracao.perguntaOnze === true || autuado.ocorrenciaInfracao.perguntaOnze === false)\"><label class=\"p-r-25 full-width\">12) O ocupante atual é regularizado pela SPU, porém, está Inadimplente com as taxas contratuais ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaDoze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong>para a desocupação do imóvel em 30 dias com base nos arts. 1.º e 11 da Lei 9636/98 e nos arts. 132, 88 e 89 § 2.º \"a\" do Decreto-Lei 9760/46 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaDoze === false\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaTreze ng-change=bemUsoDominialPerguntaTreze(autuado.ocorrenciaInfracao.perguntaTreze) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaDoze === false\"><label class=\"p-r-25 full-width\">13) O ocupante atual é regularizado pela SPU, foi comprovado a ocorrência de crime ambiental na área, conforme manifestação do órgão ambiental responsável ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaTreze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong> para a desocupação imediata do imóvel com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaTreze != null && (autuado.ocorrenciaInfracao.perguntaTreze === true || autuado.ocorrenciaInfracao.perguntaTreze === false)\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaQuatorze ng-change=bemUsoDominialPerguntaQuatorze(autuado.ocorrenciaInfracao.perguntaQuatorze) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaTreze != null && (autuado.ocorrenciaInfracao.perguntaTreze === true || autuado.ocorrenciaInfracao.perguntaTreze === false)\"><label class=\"p-r-25 full-width\">14) O ocupante atual é regularizado pela SPU e foi detectado crime contra o patrimônio (parcelamento irregular de solo sem autorização da União, demolição de benfeitoria da União sem autorização, dentre outros) ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatorze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deve-se aplicar <strong>Notificação</strong>para a desocupação imediata do imóvel com base nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div></div><div ng-show=\"autuado.ocorrenciaInfracao.perguntaQuatorze === false\"><div layout=row layout-xs=column><md-radio-group layout=row class=\"radio-group-custom md-block m-t-20 m-b-20\" ng-model=autuado.ocorrenciaInfracao.perguntaQuinze ng-change=bemUsoDominialPerguntaQuinze(autuado.ocorrenciaInfracao.perguntaQuinze) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaQuatorze === false\"><label class=\"p-r-25 full-width\">15) O ocupante atual é regularizado pela SPU com instrumento precário e o imóvel foi solicitado para uso em serviço público ?</label><md-radio-button ng-value=true class=\"md-primary radio-button-custom\">Sim</md-radio-button><md-radio-button ng-value=false style=\"outline: none\" class=\"md-primary radio-button-custom\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaQuinze === true\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=75><p>Deverá ser aplicada uma <strong>Notificação</strong> para a desocupação do imóvel em 90 dias, se urbano, ou em 180 dias, se rural, com base no arts. 132 e 89 § 3.º “a” e \"b\" do Decreto-lei 9760/46, nos arts. 1.º e 11 da Lei 9636/98 e arts. 31 a 34 da Instrução Normativa nº 23, de 18 de março de 2020, da Secretaria de Coordenação e Governança do Patrimônio da União.</p></md-input-container></div><div layout=row layout-xs=column ng-show=\"autuado.ocorrenciaInfracao.perguntaQuinze === true\"><md-radio-group layout=row class=md-block ng-model=autuado.ocorrenciaInfracao.tipoNotificacaoSelected ng-change=handleTipoNotificacaoSelected(autuado.ocorrenciaInfracao.tipoNotificacaoSelected) flex-gt-xs flex=75 ng-disabled=(apenasLeitura) style=\"height: 20px;margin-top: 10px\" ng-required=\"autuado.ocorrenciaInfracao.perguntaQuinze === true\"><md-radio-button ng-value=true class=md-primary>Notificação para desocupação em 90 dias</md-radio-button><md-radio-button ng-value=false class=md-primary>Notificação para desocupação em 180 dias</md-radio-button></md-radio-group></div></div><div layout=row layout-xs=column ng-show=autuado.ocorrenciaInfracao.areaAutuada><md-input-container class=\"md-block m-l-0\" flex-gt-xs flex=25><label><span>Área Autuada (m²)</span></label><input ng-model=autuado.ocorrenciaInfracao.areaAutuada ui-number-mask=2 type=text maxlength=80 ng-disabled=true></md-input-container></div><div layout=row layout-xs=column class=\"m-t-15 infracao-titulo\"><div flex><h3 class=\"no-margin m-t-10\">Infrações</h3></div></div><div layout=row layout-xs=column class=\"m-b-10 m-t-10\"><md-table-container class=md-block flex-gt-xs flex=100><table md-table><thead md-head><tr md-row><th md-column class=text-left><span>Nome do Documento</span></th><th md-column width=100px class=text-center><span>Ações</span></th></tr></thead><tbody md-body><tr md-row ng-repeat=\"documento in autuado.documentos\" ng-show=\"autuado.documentos.length > 0\"><td md-cell class=text-left><span ng-show=\"(documento.status != \'DOCUMENTO_EXCLUIDO\') || (!documento.status)\">{{tiposDocumento[documento.tipoDocumento]}}</span> <span ng-show=\"documento.status == \'DOCUMENTO_EXCLUIDO\'\">{{tiposDocumento[documento.tipoDocumento]}} - Excluído - Justificativa: <span class=text-bold>{{documento.justificativaExclusao}}</span></span></td><td md-cell class=text-right><div class=\"container-acoes full-width\"><md-fab-speed-dial class=\"md-scale md-hover-full\" md-direction=left><md-fab-trigger><md-button aria-label=menu class=md-fab><md-icon ng-bind=\"\'more_vert\'\"></md-icon></md-button></md-fab-trigger><md-fab-actions><md-button class=\"md-fab md-raised md-mini\" ng-href=/fiscalizacao/api/documentos/download/{{documento.id}} target=_blank ng-show=documento.id><md-icon ng-bind=\"\'picture_as_pdf\'\"><md-tooltip>Imprimir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"excluirDocumento(documento, $index, $event)\" ng-show=\"documento.status != \'AUTO_EXCLUIDO\' && documento.id\"><md-icon ng-bind=\"\'delete\'\"><md-tooltip>Excluir</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"updateDocumentoDominial(documento, $index, $event)\" ng-show=\"documento.status != \'AUTO_EXCLUIDO\'\"><md-icon ng-bind=\"\'create\'\"><md-tooltip>Alterar</md-tooltip></md-icon></md-button><md-button class=\"md-fab md-raised md-mini\" ng-click=\"visualizarDocumento(documento, $event)\"><md-icon ng-bind=\"\'search\'\"><md-tooltip>Visualizar</md-tooltip></md-icon></md-button></md-fab-actions></md-fab-speed-dial></div></td></tr><tr md-row ng-show=\"!autuado.documentos.length > 0 || scope.showMensagem === true\" style=\"height: 63px\"><td md-cell class=text-left colspan=100%>Nenhum documento registrado!</td></tr></tbody></table></md-table-container></div></div>");
$templateCache.put("scripts-fiscalizacao/pages/gerenciadorInfracao/gerenciarRecurso/controlePrazo/view/controle-prazo.html","<md-dialog style=\"width: 1000px !important\"><md-toolbar class=m-b-20><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Controle de Prazo</span><md-button class=md-icon-button ng-click=scope.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><form ng-cloak name=formRegistro ng-submit=scope.persistirRegistro(scope._registro) novalidate><md-dialog-content><div id=su-fiscalizacao><div layout=row layout-xs=column><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=100><p><strong>1-</strong> Apresentou recurso em primeira intância?</p></md-input-container></div><div layout=row layout-xs=column><md-radio-group layout=row ng-model=scope._registro.primeiraInstancia ng-change=scope.handlePrimeiraInstanciaSelected(scope._registro.primeiraInstancia) class=\"md-block radio-button-column\" flex-gt-xs flex=100 ng-disabled=\"scope.apenasLeitura || scope.disableInstancia\" required><md-radio-button layout=row ng-value=true class=\"md-primary m-b-15 m-t-15\">Sim</md-radio-button><div class=prazo-entrega-margin ng-show=scope._registro.primeiraInstancia><div layout=row layout-xs=column class=m-t-15><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data do recurso*</label><md-datepicker-custom name=dataRecursoPrimeiraInstancia ng-model=scope._registro.dataRecursoPrimeiraInstancia ng-change=scope.validarPrazoRecursoSessentaDias(scope._registro.dataRecursoPrimeiraInstancia) class=md-block flex-gt-xs flex=30 md-custom-mask=39/19/9999 md-custom-clean-mask=false md-custom-restrict=reject md-custom-invalid=false ng-disabled=scope.apenasLeitura ng-required=\"scope._registro.primeiraInstancia === true\"></md-datepicker-custom></div><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope._registro.processoAdminPrimeiraInstancia class=md-input ui-mask=9999999 ng-disabled=scope.apenasLeitura ng-required=scope._registro.primeiraInstancia></md-input-container></div><div layout=row layout-xs=column><md-checkbox class=\"m-b-30 md-primary\" ng-model=scope._registro.enviouJuridico ng-change=scope.handleJuridicoSelected(scope._registro.enviouJuridico) ng-disabled=\"scope.apenasLeitura || !scope._registro.id\" flex-gt-xs flex=100><span>Enviou para o jurídico</span></md-checkbox></div><div class=prazo-entrega-margin ng-show=scope._registro.enviouJuridico><div layout=row layout-xs=column><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data de envio</label><md-datepicker-custom name=dataEnvioJuridico ng-model=scope._registro.dataEnvioJuridico ng-required=\"scope._registro.enviouJuridico == true\" md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject ng-disabled=scope.disableEnvioJuridico()></md-datepicker-custom></div><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope._registro.processoAdminJuridico ng-required=scope._registro.enviouJuridico class=md-input ui-mask=9999999 md-custom-invalid=false ng-disabled=scope.disableEnvioJuridico()></md-input-container></div></div><div ng-show=\"scope._registro.dataRecursoPrimeiraInstancia && scope._registro.processoAdminPrimeiraInstancia && scope.showResultado\"><div layout=row layout-xs=column><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=100><p>Resultado da análise em primeira instância.</p></md-input-container></div><div layout=row layout-xs=column><md-radio-group layout=row class=md-block ng-model=scope.situacaoPrimeiraInstancia ng-change=scope.handleSituacaoPrimeiraInstancia(scope.situacaoPrimeiraInstancia) flex-gt-xs flex=100 ng-disabled=\"scope.apenasLeitura || !scope._registro.id\"><md-radio-button layout=row ng-value=\"\'deferido\'\" ng-model=scope.situacaoPrimeiraInstancia ng-checked=\"(scope.situacaoPrimeiraInstancia == \'deferido\')\" class=\"md-primary m-b-15 m-t-15\">Deferido</md-radio-button><md-radio-button layout=row ng-value=\"\'deferidoParcialmente\'\" ng-model=scope.situacaoPrimeiraInstancia ng-checked=\"(scope.situacaoPrimeiraInstancia == \'deferidoParcialmente\')\" class=\"md-primary m-b-15 m-t-15\">Deferido parcialmente</md-radio-button><md-radio-button layout=row ng-value=\"\'indeferido\'\" ng-model=scope.situacaoPrimeiraInstancia ng-checked=\"(scope.situacaoPrimeiraInstancia == \'indeferido\')\" class=\"md-primary m-b-15 m-t-15\">Indeferido</md-radio-button></md-radio-group></div><div layout=row layout-xs=column class=m-t-15><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data de entrega da resposta do recurso</label><md-datepicker-custom name=dataEntregaPrimeiraInstancia ng-model=scope._registro.dataEntregaPrimeiraInstancia md-custom-invalid=false ng-required=scope.situacaoPrimeiraInstancia md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject ng-disabled=scope.disableResultadoPrimeiraInstancia()></md-datepicker-custom></div><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope._registro.processoAdminPrimeiraInstanciaResultado ng-required=scope.situacaoPrimeiraInstancia class=md-input ui-mask=9999999 ng-disabled=scope.disableResultadoPrimeiraInstancia()></md-input-container></div></div></div><md-radio-button layout=row ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div><div ng-show=scope.situacaoPrimeiraInstancia><div layout=row layout-xs=column class=m-t-20><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=100><p><strong>2-</strong> Apresentou recurso em segunda instância?</p></md-input-container></div><div layout=row layout-xs=column><md-radio-group layout=row ng-model=scope._registro.segundaInstancia ng-change=\"scope.handleSegundaInstaciaSelected(scope._registro.segundaInstancia); scope.disableCamposSegundaInstancia(scope._registro.segundaInstancia)\" class=\"md-block radio-button-column\" flex-gt-xs flex=100 ng-disabled=\"scope.apenasLeitura || scope.disableInstancia\" required><md-radio-button layout=row ng-value=true class=\"md-primary m-b-15 m-t-15\">Sim</md-radio-button><div class=prazo-entrega-margin ng-show=scope._registro.segundaInstancia><div layout=row layout-xs=column><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data do recurso</label><md-datepicker-custom name=dataRecursoSegundaInstancia ng-model=scope._registro.dataRecursoSegundaInstancia md-custom-invalid=false ng-change=scope.validarRecursoDezDias(scope._registro.dataRecursoSegundaInstancia) md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject ng-required=scope._registro.segundaInstacia></md-datepicker-custom></div><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope._registro.processoAdminSegundaInstancia class=md-input ui-mask=9999999 ng-disabled=scope.apenasLeitura ng-required=scope._registro.segundaInstancia></md-input-container></div><div ng-show=scope.showReconsideracao><div layout=row layout-xs=column><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=100><p>Houve reconsideração pelo superintendente?</p></md-input-container></div><div layout=row layout-xs=column><md-radio-group layout=row class=\"md-block radio-button-column m-t-0\" ng-model=scope._registro.reconsideracao ng-change=scope.handleReconsideracaoSelected(scope._registro.reconsideracao) flex-gt-xs flex=100 ng-disabled=\"scope.apenasLeitura || scope.campoDisable\"><md-radio-button layout=row ng-value=true class=\"md-primary m-b-15 m-t-15\">Sim</md-radio-button><div class=prazo-entrega-margin><div layout=row layout-xs=column><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data de entrega da resposta do recurso</label><md-datepicker-custom name=dataEntregaRecursoSegundaInstancia md-custom-invalid=false ng-model=scope._registro.dataEntregaRecursoSegundaInstancia ng-required=scope._registro.reconsideracao ng-disabled=\"scope.apenasLeitura || scope.campoDisable || !scope._registro.reconsideracao\" md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject></md-datepicker-custom></div><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope._registro.processoAdminReconsideracao ng-required=scope._registro.processoAdminReconsideracao class=md-input ui-mask=9999999 ng-disabled=\"scope.apenasLeitura || scope.campoDisable || !scope._registro.reconsideracao\"></md-input-container></div></div><md-radio-button layout=row ng-value=false class=\"md-primary m-b-15 m-t-15\">Não</md-radio-button></md-radio-group></div></div><div layout=row layout-xs=column class=m-t-15><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data de encaminhamento ao órgão central</label><md-datepicker-custom name=dataEncaminhamento ng-model=scope._registro.dataEncaminhamento md-custom-invalid=false ng-disabled=\"scope.apenasLeitura || scope.campoDisable\" md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject></md-datepicker-custom></div><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope._registro.processoAdminEncaminhamento ng-disalbed=\"scope.apenasLeitura || scope.campoDisable\" class=md-input ui-mask=9999999></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=100><p>Resultado da análise em segunda instância.</p></md-input-container></div><div ng-show=\"scope._registro.reconsideracao == false\"><div layout=row layout-xs=column><md-radio-group layout=row class=md-block ng-model=scope.situacaoSegundaInstancia ng-change=scope.handleSituacaoSegundaInstancia(scope.situacaoSegundaInstancia) flex-gt-xs flex=100 ng-disabled=\"scope.apenasLeitura || scope.campoDisable\"><md-radio-button layout=row ng-value=\"\'deferido\'\" ng-model=scope.situacaoSegundaInstancia ng-checked=\"(scope.situacaoSegundaInstancia == \'deferido\')\" class=\"md-primary m-b-15 m-t-15\">Deferido</md-radio-button><md-radio-button layout=row ng-value=\"\'deferidoParcialmente\'\" ng-model=scope.situacaoSegundaInstancia ng-checked=\"(scope.situacaoSegundaInstancia == \'deferidoParcialmente\')\" class=\"md-primary m-b-15 m-t-15\">Deferido parcialmente</md-radio-button><md-radio-button layout=row ng-value=\"\'indeferido\'\" ng-model=scope.situacaoSegundaInstancia ng-checked=\"(scope.situacaoSegundaInstancia == \'indeferido\')\" class=\"md-primary m-b-15 m-t-15\">Indeferido</md-radio-button></md-radio-group></div><div layout=row layout-xs=column class=m-t-15 ng-if=\"scope._registro.reconsideracao == false\"><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data de entrega da resposta do recurso</label><md-datepicker-custom name=dataEntregaResultadoSegundaInstancia ng-model=scope._registro.dataEntregaResultadoSegundaInstancia md-custom-mask=39/19/9999 md-custom-clean-mask=true md-custom-restrict=reject md-custom-invalid=false md-custom-required=false></md-datepicker-custom></div><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número do documento no SEI</span></label><input type=text ng-model=scope._registro.processoAdminSegundaInstanciaResultado ng-required=scope.situacaoSegundaInstancia ng-disabled=\"scope.apenasLeitura || scope.campoDisable\" class=md-input ui-mask=9999999 ng-disabled=scope.apenasLeitura></md-input-container></div></div></div><md-radio-button layout=row ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div></div></div></md-dialog-content><md-dialog-actions layout=row><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=\"md-raised md-primary m-r-0 m-t-0\" ng-click=scope.cancelModal()>Fechar</md-button><md-button type=submit class=\"md-raised md-primary btn-form-action m-r-0\" ng-show=!scope.apenasLeitura>Salvar</md-button></div></section></div></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-fiscalizacao/pages/gerenciadorInfracao/gerenciarRecurso/detalheAuto/view/detalhe-auto.html","<md-dialog style=\"width: 1000px !important\"><md-toolbar class=m-b-20><div class=md-toolbar-tools><span style=\"font-size: 18px\" flex>Formulário de Sanção</span><md-button class=md-icon-button ng-click=scope.cancelModal()><ng-md-icon style=\"fill: #fff\" icon=close></ng-md-icon></md-button></div></md-toolbar><form ng-cloak name=formDocumento ng-submit=scope.persistirRegistro(scope.documento) novalidate><md-dialog-content><div id=su-fiscalizacao><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100><label>Tipo de Documento</label><md-select ng-model=scope.documento.tipoDocumento ng-disabled=true required><md-option ng-value=k ng-repeat=\"(k,v) in scope._tiposDocumento track by $index\">{{v}}</md-option></md-select></md-input-container></div><div ng-show=scope.documento.tipoDocumento><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-show=\"scope.documento.tipoDocumento ==\'NOTIFICACAO\'\"><label>Tipo de Notificação</label><md-select ng-model=scope.documento.tipoNotificacao ng-disabled=scope.apenasLeitura ng-required=\"scope.documento.tipoDocumento ==\'NOTIFICACAO\'\"><md-option ng-value=k ng-repeat=\"(k,v) in scope._tiposNotificacao track by $index\">{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column class=m-b-20><md-radio-group layout=row ng-model=scope.documento.uploadArquivo name=upload class=\"md-block m-b-20\" flex-gt-xs flex=100 style=\"align-self: center\" ng-disabled=scope.apenasLeitura ng-change=scope.changeUpload(scope.documento.uploadArquivo)><label class=p-r-25>Deseja realizar upload de arquivo?</label><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group><md-radio-group layout=row class=\"md-block m-b-20 fileUpload md-raised md-primary btn-form-action\" ng-if=\"scope.documento.uploadArquivo && !scope.documento.arquivo.id\" flex-gt-xs flex=100 style=\"height: 44px !important\"><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=scope.adicionarArquivoDocumento($files) ng-disabled=scope.apenasLeitura><label>Carregar Arquivo</label></md-button></md-radio-group><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-if=scope.documento.arquivo.id><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope.documento.arquivo.id}}><strong>{{scope.documento.arquivo.nome}}</strong></a></md-input-container></div><div ng-show=!scope.documento.uploadArquivo><div layout=row layout-xs=column class=m-b-40><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data da Autuação</label><md-datepicker-custom flex-gt-xs flex=100 ng-model=scope.documento.dataAutuacao md-min-date=scope.minDataFiscalizacao name=dataAutuacao class=md-block flex-gt-xs md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=false ng-disabled=scope.apenasLeitura></md-datepicker-custom><div class=\"md-block validation-messages\" flex-gt-xs flex=100| style=\"color:red; font-size: 12px\"><div ng-show=formDocumento.dataAutuacao.$error.mindate>Escolha uma data maior que a data início da fiscalização</div></div></div><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Hora da Autuacao</label><input input-horario type=text ng-model=scope.documento.horaAutuacao class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento != \'NOTIFICACAO\'\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100><label>{{scope.documento.tipoDocumento == \'AUTO_DE_EMBARGO\' ? \'Área de Embargo (m²)\' : \'Área da Infração\'}}</label><input ui-number-mask type=text ng-model=scope.documento.areaEmbargo class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>CNPJ/CPF do responsável</label><input ui-br-cpfcnpj-mask type=text ng-model=scope.documento.responsavel.cpfCnpj class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nome do responsável</label><input type=text ng-model=scope.documento.responsavel.nome maxlength=100 ng-maxlength=100 class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Documento de Identificação</label><md-select ng-model=scope.documento.documentoIdentificacao ng-disabled=scope.apenasLeitura ng-change=scope.mudaDocumentoIdentificacao(documento.documentoIdentificacao)><md-option ng-value=k ng-repeat=\"(k,v) in scope._documentosIdentificacao track by $index\">{{ v }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50 ng-show=\"scope.documento.documentoIdentificacao != \'PASSAPORTE\'\"><label>Número do Documento</label><input type=text ui-number-mask=0 ng-model=scope.documento.numeroDocumento class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50 ng-show=\"scope.documento.documentoIdentificacao == \'PASSAPORTE\'\"><label>Número do Documento</label><input type=text ng-model=scope.documento.numeroDocumento class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.documentoIdentificacao == \'PASSAPORTE\'\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100><label>País Emissor</label><input type=text ng-model=scope.documento.paisEmissor maxlength=150 ng-maxlength=150 class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.documentoIdentificacao == \'RG\'\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Orgão Emissor</label><input type=text ng-model=scope.documento.orgaoEmissor maxlength=150 ng-maxlength=150 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label>UF de Emissão</label><md-select ng-model=scope.documento.ufEmissao ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._estados\">{{ item.sigla }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Filiação</label><input type=text ng-model=scope.documento.filiacao maxlength=200 ng-maxlength=200 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data de Nascimento</label><md-datepicker-custom flex-gt-xs flex=100 name=dataNascimento ng-model=scope.documento.dataNascimento class=md-input flex-gt-xs md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true ng-disabled=scope.apenasLeitura></md-datepicker-custom></div></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nacionalidade</label><input type=text ng-model=scope.documento.nacionalidade maxlength=100 ng-maxlength=100 class=md-input ng-required=false ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Naturalidade</label><input type=text ng-model=scope.documento.naturalidade maxlength=100 ng-maxlength=100 class=md-input ng-required=false ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column class=m-b-20><md-radio-group layout=row ng-model=scope.documento.confirmaEndereco name=confirmaEndereco ng-change=scope.documentoEnderecoConfirmado() class=md-block flex-gt-xs flex=100 ng-disabled=scope.apenasLeitura><label class=p-r-25>Endereço do Imóvel é o mesmo para correspondência?</label><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div><div ng-show=!scope.documento.confirmaEndereco><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>UF</label><input type=text ng-model=scope.documento.endereco.estado class=md-input ng-required=!scope.documento.confirmaEndereco disabled></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>CEP</label><input type=text ng-model=scope.documento.endereco.cep ng-blur=scope.getCEPDocumento(scope.documento.endereco.cep) ui-br-cep-mask class=md-input ng-required=!scope.documento.confirmaEndereco ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label>Município</label><md-select ng-model=scope.documento.endereco.municipio ng-disabled=\"scope.apenasLeitura || scope.desabilitaMunicipio\" ng-required=!scope.documento.confirmaEndereco><md-option ng-value=item ng-repeat=\"item in scope._municipiosDocumento\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Destinação</label><md-select ng-model=scope.documento.endereco.tipoImovel ng-required=!scope.documento.confirmaEndereco ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._tiposImovel\">{{ item.nome }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label>Tipo de logradouro</label><md-select ng-model=scope.documento.endereco.tipoLogradouro ng-required=!scope.documento.confirmaEndereco ng-disabled=scope.apenasLeitura><md-option ng-value=k ng-repeat=\"(k,y) in scope._tipoLogradouro\">{{y}}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Bairro/Distrito</label><input type=text ng-model=scope.documento.endereco.bairro class=md-input ng-required=!scope.documento.confirmaEndereco maxlength=100 ng-maxlength=100 ng-disabled=\"scope.apenasLeitura  || scope.desabilitaBairro\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nº</label><input type=text ng-model=scope.documento.endereco.numero class=md-input ng-required=!scope.documento.confirmaEndereco ng-maxlength=100 maxlength=1000 ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Logradouro</label><input type=text ng-model=scope.documento.endereco.logradouro class=md-input ng-required=!scope.documento.confirmaEndereco ng-disabled=\"scope.apenasLeitura  || scope.desabilitaLogradouro\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Natureza do imóvel</label><md-select ng-model=scope.documento.endereco.zoneamento ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._zoneamentos\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Complemento</label><input type=text ng-model=scope.documento.endereco.complemento class=md-input ng-required=!scope.documento.confirmaEndereco maxlength=200 ng-maxlength=200 ng-disabled=scope.apenasLeitura></md-input-container></div></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Telefone para Contato</label><input type=text ui-br-phone-number=\"\" ng-model=scope.documento.telefoneContato class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Email para Contato</label><input type=text ng-model=scope.documento.emailContato class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><label>Descrição do Fato</label><textarea ng-model=scope.documento.descricaoFato style=\"opacity: 0.6\" maxlength=1000 md-maxlength=1000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura></textarea></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.justificativaEdicao && !scope.documento.justificativaExclusao\"><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa da Edição</label><textarea ng-model=scope.documento.justificativaEdicao style=\"opacity: 0.6\" maxlength=1000 md-maxlength=1000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura></textarea></md-input-container></div><div layout=row layout-xs=column ng-show=scope.documento.justificativaExclusao><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa da Exclusão</label><textarea ng-model=scope.documento.justificativaExclusao style=\"opacity: 0.6\" maxlength=1000 md-maxlength=1000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura></textarea></md-input-container></div><div ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><div layout=row layout-xs=column><h3 class=m-t-20>Sanções Administrativas</h3></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><h4 class=m-t-20>Selecione as sanções aplicadas</h4></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.embargo ng-disabled=scope.apenasLeitura>Embargo da Obra serviço ou atividade;</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.demolicao ng-disabled=scope.apenasLeitura>Demolição e/ou remoção de aterro, construção, obra, cercas, ou demais benfeitorias, bem os equipamentos instalados, à conta de quem os houver efetuado, caso não sejam passíveis de regularização.</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.multa ng-disabled=\"scope.documento.sancaoAdministrativa.blockMulta == true\">Multa mensal</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=scope.documento.sancaoAdministrativa.multa><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Área (m²)</label><input type=text ui-number-mask ng-model=scope.documento.sancaoAdministrativa.areaMulta ng-change=scope.calcularMulta() class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.multa></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Valor definido para m²:</label><input type=text ui-money-mask=2 ng-model=scope.documento.sancaoAdministrativa.valorMulta.valorMulta ng-change=scope.calcularMulta() disabled class=md-input ng-required=scope.documento.sancaoAdministrativa.multa></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Valor total da Multa</label><input type=text ui-money-mask=2 ng-model=scope.documento.sancaoAdministrativa.totalMulta class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.multa></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.desocupacao ng-disabled=\"scope.documento.sancaoAdministrativa.blockIndenizacao == true || scope.apenasLeitura\">Desocupação de imóvel com pagamento de indenização</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=scope.documento.sancaoAdministrativa.desocupacao><div flex-gt-xs flex=100><div layout=row layout-xs=column><h4 class=m-t-20>Período de Ocupação</h4></div><div layout=row layout-xs=column ng-show=scope.documento.sancaoAdministrativa.desocupacao><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano inicial</label><input ng-required=scope.documento.sancaoAdministrativa.desocupacao ui-hide-group-sep ui-number-mask=0 type=text ng-keyup=scope.validaTamanhoAno() ng-blur=scope.validaAnoInicial(documento.sancaoAdministrativa.anoInicialOcupacao) maxlength=4 ng-model=scope.documento.sancaoAdministrativa.anoInicialOcupacao ng-maxlength=4 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano final</label><input ng-required=scope.documento.sancaoAdministrativa.desocupacao ui-hide-group-sep type=text ui-number-mask=0 ng-keyup=scope.validaTamanhoAno() ng-blur=scope.validaAnoFinal(documento.sancaoAdministrativa.anoFinalOcupacao) maxlength=4 ng-model=scope.documento.sancaoAdministrativa.anoFinalOcupacao ng-maxlength=4 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Área do terreno (m²)</label><input ng-required=scope.documento.sancaoAdministrativa.desocupacao type=text ui-number-mask ng-model=scope.documento.sancaoAdministrativa.areaOcupacao class=md-input ng-disabled=scope.apenasLeitura ng-change=scope.calcularIndenizacaoArea(scope.documento.sancaoAdministrativa.multas)></md-input-container></div><div layout=row layout-xs=column><h4 class=m-t-20>Período de Cobrança</h4></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano inicial</label><input type=text ui-number-mask=0 ui-hide-group-sep maxlength=4 ng-maxlength=4 ng-blur=scope.validaIntervaloCobranca() ng-model=scope.documento.sancaoAdministrativa.anoInicialCobranca class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano final</label><input type=text ui-number-mask=0 ui-hide-group-sep maxlength=4 ng-maxlength=4 ng-change=scope.validaIntervaloCobranca() ng-model=scope.documento.sancaoAdministrativa.anoFinalCobranca class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33></md-input-container></div><div layout=row layout-xs=column ng-repeat=\"multa in scope.documento.sancaoAdministrativa.multas\" style=\"padding-left: 30px\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Planta de Valores Genéricos para {{multa.ano}} (R$/m²)</label><input type=text ui-money-mask=2 ng-change=scope.calculaVdp(multa) ng-model=multa.plantaValoresGenericos class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>VDP</label><input type=text ui-money-mask=2 ng-model=multa.valorDominioPleno class=md-input disabled ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Indenização em {{multa.ano}}</label><input type=text ui-money-mask=2 ng-model=multa.indenizacao class=md-input disabled></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=75></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=25 style=\"text-align: right\"><label style=\"text-align: right; margin-left: 53px\">Valor total da Indenização</label><span><strong>{{scope.documento.sancaoAdministrativa.valorTotalIndenizacao | currency: \"R$ \": 2}}</strong></span></md-input-container></div></div></div></div></div></div></div></md-dialog-content><md-dialog-actions layout=row><div class=\"sgr-container-buttons sgr-container-footer\" layout=row layout-align=end><section layout=\"row center\" layout-sm=column layout-wrap><div layout=row layout-align=\"space-around center\"><md-button class=\"md-raised md-primary m-r-0 m-t-0\" ng-click=scope.cancelModal() ng-disabled=scope.realizandoUpload>Fechar</md-button><md-button type=submit class=\"md-raised md-primary btn-form-action m-r-0\" ng-show=!scope.apenasLeitura>Confirmar</md-button></div></section></div></md-dialog-actions></form></md-dialog>");
$templateCache.put("scripts-fiscalizacao/pages/gerenciadorInfracao/sancao/detalheSansao/view/detalhe-sancao.html","<form ng-cloak name=formRegistro ng-submit=scope.persistirRegistro(scope._registro) novalidate><div layout=row layout-xs=column><div flex><h2>Detalhes da sanção</h2></div></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100><label>Tipo de Documento</label><md-select ng-model=scope.documento.tipoDocumento ng-disabled=true required><md-option ng-value=k ng-repeat=\"(k,v) in scope._tiposDocumento track by $index\">{{v}}</md-option></md-select></md-input-container></div><div ng-show=scope.documento.tipoDocumento><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-show=\"scope.documento.tipoDocumento ==\'NOTIFICACAO\'\"><label>Tipo de Notificação</label><md-select ng-model=scope.documento.tipoNotificacao ng-disabled=scope.apenasLeitura ng-required=\"scope.documento.tipoDocumento ==\'NOTIFICACAO\'\"><md-option ng-value=k ng-repeat=\"(k,v) in scope._tiposNotificacao track by $index\">{{v}}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column class=m-b-20><md-radio-group layout=row ng-model=scope.documento.uploadArquivo name=upload class=\"md-block m-b-20\" flex-gt-xs flex=100 style=\"align-self: center\" ng-disabled=scope.apenasLeitura ng-change=scope.changeUpload(scope.documento.uploadArquivo)><label class=p-r-25>Deseja realizar upload de arquivo?</label><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group><md-radio-group layout=row class=\"md-block m-b-20 fileUpload md-raised md-primary btn-form-action\" ng-if=\"scope.documento.uploadArquivo && !scope.documento.arquivo.id\" flex-gt-xs flex=100 style=\"height: 44px !important\"><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=scope.adicionarArquivoDocumento($files) ng-disabled=scope.apenasLeitura><label>Carregar Arquivo</label></md-button></md-radio-group><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100 ng-if=scope.documento.arquivo.id><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope.documento.arquivo.id}}><strong>{{scope.documento.arquivo.nome}}</strong></a></md-input-container></div><div ng-show=!scope.documento.uploadArquivo><div layout=row layout-xs=column class=m-b-40><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data da Autuação</label><md-datepicker-custom flex-gt-xs flex=100 ng-model=scope.documento.dataAutuacao md-min-date=scope.minDataFiscalizacao name=dataAutuacao class=md-block flex-gt-xs md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=false ng-disabled=scope.apenasLeitura></md-datepicker-custom><div class=\"md-block validation-messages\" flex-gt-xs flex=100| style=\"color:red; font-size: 12px\"><div ng-show=formDocumento.dataAutuacao.$error.mindate>Escolha uma data maior que a data início da fiscalização</div></div></div><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Hora da Autuacao</label><input input-horario type=text ng-model=scope.documento.horaAutuacao class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento != \'NOTIFICACAO\'\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100><label>{{scope.documento.tipoDocumento == \'AUTO_DE_EMBARGO\' ? \'Área de Embargo (m²)\' : \'Área da Infração\'}}</label><input ui-number-mask type=text ng-model=scope.documento.areaEmbargo class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>CNPJ/CPF do responsável</label><input ui-br-cpfcnpj-mask type=text ng-model=scope.documento.responsavel.cpfCnpj class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nome do responsável</label><input type=text ng-model=scope.documento.responsavel.nome maxlength=100 ng-maxlength=100 class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Documento de Identificação</label><md-select ng-model=scope.documento.documentoIdentificacao ng-disabled=scope.apenasLeitura ng-change=scope.mudaDocumentoIdentificacao(documento.documentoIdentificacao)><md-option ng-value=k ng-repeat=\"(k,v) in scope._documentosIdentificacao track by $index\">{{ v }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50 ng-show=\"scope.documento.documentoIdentificacao != \'PASSAPORTE\'\"><label>Número do Documento</label><input type=text ui-number-mask=0 ng-model=scope.documento.numeroDocumento class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50 ng-show=\"scope.documento.documentoIdentificacao == \'PASSAPORTE\'\"><label>Número do Documento</label><input type=text ng-model=scope.documento.numeroDocumento class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.documentoIdentificacao == \'PASSAPORTE\'\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=100><label>País Emissor</label><input type=text ng-model=scope.documento.paisEmissor maxlength=150 ng-maxlength=150 class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.documentoIdentificacao == \'RG\'\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Orgão Emissor</label><input type=text ng-model=scope.documento.orgaoEmissor maxlength=150 ng-maxlength=150 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><label>UF de Emissão</label><md-select ng-model=scope.documento.ufEmissao ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._estados\">{{ item.sigla }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Filiação</label><input type=text ng-model=scope.documento.filiacao maxlength=200 ng-maxlength=200 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><div class=\"md-block datepicker-horario\" flex-gt-xs flex=50><label>Data de Nascimento</label><md-datepicker-custom flex-gt-xs flex=100 name=dataNascimento ng-model=scope.documento.dataNascimento class=md-input flex-gt-xs md-custom-required=false md-custom-mask=39/19/9999 md-custom-invalid=true ng-disabled=scope.apenasLeitura></md-datepicker-custom></div></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nacionalidade</label><input type=text ng-model=scope.documento.nacionalidade maxlength=100 ng-maxlength=100 class=md-input ng-required=false ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Naturalidade</label><input type=text ng-model=scope.documento.naturalidade maxlength=100 ng-maxlength=100 class=md-input ng-required=false ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column class=m-b-20><md-radio-group layout=row ng-model=scope.documento.confirmaEndereco name=confirmaEndereco ng-change=scope.documentoEnderecoConfirmado() class=md-block flex-gt-xs flex=100 ng-disabled=scope.apenasLeitura><label class=p-r-25>Endereço do Imóvel é o mesmo para correspondência?</label><md-radio-button ng-value=true class=md-primary>Sim</md-radio-button><md-radio-button ng-value=false class=md-primary>Não</md-radio-button></md-radio-group></div><div ng-show=!scope.documento.confirmaEndereco><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>UF</label><input type=text ng-model=scope.documento.endereco.estado class=md-input ng-required=!scope.documento.confirmaEndereco disabled></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>CEP</label><input type=text ng-model=scope.documento.endereco.cep ng-blur=scope.getCEPDocumento(scope.documento.endereco.cep) ui-br-cep-mask class=md-input ng-required=!scope.documento.confirmaEndereco ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label>Município</label><md-select ng-model=scope.documento.endereco.municipio ng-disabled=\"scope.apenasLeitura || scope.desabilitaMunicipio\" ng-required=!scope.documento.confirmaEndereco><md-option ng-value=item ng-repeat=\"item in scope._municipiosDocumento\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Destinação</label><md-select ng-model=scope.documento.endereco.tipoImovel ng-required=!scope.documento.confirmaEndereco ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._tiposImovel\">{{ item.nome }}</md-option></md-select></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label>Tipo de logradouro</label><md-select ng-model=scope.documento.endereco.tipoLogradouro ng-required=!scope.documento.confirmaEndereco ng-disabled=scope.apenasLeitura><md-option ng-value=k ng-repeat=\"(k,y) in scope._tipoLogradouro\">{{y}}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Bairro/Distrito</label><input type=text ng-model=scope.documento.endereco.bairro class=md-input ng-required=!scope.documento.confirmaEndereco maxlength=100 ng-maxlength=100 ng-disabled=\"scope.apenasLeitura  || scope.desabilitaBairro\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Nº</label><input type=text ng-model=scope.documento.endereco.numero class=md-input ng-required=!scope.documento.confirmaEndereco ng-maxlength=100 maxlength=1000 ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Logradouro</label><input type=text ng-model=scope.documento.endereco.logradouro class=md-input ng-required=!scope.documento.confirmaEndereco ng-disabled=\"scope.apenasLeitura  || scope.desabilitaLogradouro\"></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Natureza do imóvel</label><md-select ng-model=scope.documento.endereco.zoneamento ng-disabled=scope.apenasLeitura><md-option ng-value=item ng-repeat=\"item in scope._zoneamentos\">{{ item.nome }}</md-option></md-select></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Complemento</label><input type=text ng-model=scope.documento.endereco.complemento class=md-input ng-required=!scope.documento.confirmaEndereco maxlength=200 ng-maxlength=200 ng-disabled=scope.apenasLeitura></md-input-container></div></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Telefone para Contato</label><input type=text ui-br-phone-number=\"\" ng-model=scope.documento.telefoneContato class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=50><label>Email para Contato</label><input type=text ng-model=scope.documento.emailContato class=md-input ng-disabled=scope.apenasLeitura></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><label>Descrição do Fato</label><textarea ng-model=scope.documento.descricaoFato style=\"opacity: 0.6\" maxlength=1000 md-maxlength=1000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura></textarea></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.justificativaEdicao && !scope.documento.justificativaExclusao\"><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa da Edição</label><textarea ng-model=scope.documento.justificativaEdicao style=\"opacity: 0.6\" maxlength=1000 md-maxlength=1000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura></textarea></md-input-container></div><div layout=row layout-xs=column ng-show=scope.documento.justificativaExclusao><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa da Exclusão</label><textarea ng-model=scope.documento.justificativaExclusao style=\"opacity: 0.6\" maxlength=1000 md-maxlength=1000 rows=5 md-select-on-focus ng-disabled=scope.apenasLeitura></textarea></md-input-container></div><div ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><div layout=row layout-xs=column><h3 class=m-t-20>Sanções Administrativas</h3></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><h4 class=m-t-20>Selecione as sanções aplicadas</h4></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.embargo ng-disabled=scope.apenasLeitura>Embargo da Obra serviço ou atividade;</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.demolicao ng-disabled=scope.apenasLeitura>Demolição e/ou remoção de aterro, construção, obra, cercas, ou demais benfeitorias, bem os equipamentos instalados, à conta de quem os houver efetuado, caso não sejam passíveis de regularização.</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=\"scope.documento.tipoDocumento ==\'AUTO_DE_INFRACAO\'\"><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.multa ng-disabled=\"scope.documento.sancaoAdministrativa.blockMulta == true || scope.apenasLeitura\">Multa mensal</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=scope.documento.sancaoAdministrativa.multa><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Área (m²)</label><input type=text ui-number-mask ng-model=scope.documento.sancaoAdministrativa.areaMulta ng-change=scope.calcularMulta() class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.multa></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Valor definido para m²:</label><input type=text ui-money-mask=2 ng-model=scope.documento.sancaoAdministrativa.valorMulta.valorMulta ng-change=scope.calcularMulta() disabled class=md-input ng-required=scope.documento.sancaoAdministrativa.multa></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Valor total da Multa</label><input type=text ui-money-mask=2 ng-model=scope.documento.sancaoAdministrativa.totalMulta class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.multa></md-input-container></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=100><md-checkbox class=md-primary ng-model=scope.documento.sancaoAdministrativa.desocupacao ng-disabled=\"scope.documento.sancaoAdministrativa.blockIndenizacao == true || scope.apenasLeitura\">Desocupação de imóvel com pagamento de indenização</md-checkbox></md-input-container></div><div layout=row layout-xs=column ng-show=scope.documento.sancaoAdministrativa.desocupacao><div flex-gt-xs flex=100><div layout=row layout-xs=column><h4 class=m-t-20>Período de Ocupação</h4></div><div layout=row layout-xs=column ng-show=scope.documento.sancaoAdministrativa.desocupacao><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano inicial</label><input ng-required=scope.documento.sancaoAdministrativa.desocupacao ui-hide-group-sep ui-number-mask=0 type=text ng-keyup=scope.validaTamanhoAno() ng-blur=scope.validaAnoInicial(documento.sancaoAdministrativa.anoInicialOcupacao) maxlength=4 ng-model=scope.documento.sancaoAdministrativa.anoInicialOcupacao ng-maxlength=4 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano final</label><input ng-required=scope.documento.sancaoAdministrativa.desocupacao ui-hide-group-sep type=text ui-number-mask=0 ng-keyup=scope.validaTamanhoAno() ng-blur=scope.validaAnoFinal(documento.sancaoAdministrativa.anoFinalOcupacao) maxlength=4 ng-model=scope.documento.sancaoAdministrativa.anoFinalOcupacao ng-maxlength=4 class=md-input ng-disabled=scope.apenasLeitura></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Área do terreno (m²)</label><input ng-required=scope.documento.sancaoAdministrativa.desocupacao type=text ui-number-mask ng-model=scope.documento.sancaoAdministrativa.areaOcupacao class=md-input ng-disabled=scope.apenasLeitura ng-change=scope.calcularIndenizacaoArea(scope.documento.sancaoAdministrativa.multas)></md-input-container></div><div layout=row layout-xs=column><h4 class=m-t-20>Período de Cobrança</h4></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano inicial</label><input type=text ui-number-mask=0 ui-hide-group-sep maxlength=4 ng-maxlength=4 ng-blur=scope.validaIntervaloCobranca() ng-model=scope.documento.sancaoAdministrativa.anoInicialCobranca class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Ano final</label><input type=text ui-number-mask=0 ui-hide-group-sep maxlength=4 ng-maxlength=4 ng-change=scope.validaIntervaloCobranca() ng-model=scope.documento.sancaoAdministrativa.anoFinalCobranca class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33></md-input-container></div><div layout=row layout-xs=column ng-repeat=\"multa in scope.documento.sancaoAdministrativa.multas\" style=\"padding-left: 30px\"><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Planta de Valores Genéricos para {{multa.ano}} (R$/m²)</label><input type=text ui-money-mask=2 ng-change=scope.calculaVdp(multa) ng-model=multa.plantaValoresGenericos class=md-input ng-disabled=scope.apenasLeitura ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>VDP</label><input type=text ui-money-mask=2 ng-model=multa.valorDominioPleno class=md-input disabled ng-required=scope.documento.sancaoAdministrativa.desocupacao></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=33><label>Indenização em {{multa.ano}}</label><input type=text ui-money-mask=2 ng-model=multa.indenizacao class=md-input disabled></md-input-container></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=75></md-input-container><md-input-container class=\"md-input-has-placeholder md-block\" flex-gt-xs flex=25 style=\"text-align: right\"><label style=\"text-align: right; margin-left: 53px\">Valor total da Indenização</label><span><strong>{{scope.documento.sancaoAdministrativa.valorTotalIndenizacao | currency: \"R$ \": 2}}</strong></span></md-input-container></div></div></div></div></div></div></form>");
$templateCache.put("scripts-fiscalizacao/pages/gerenciadorInfracao/sancao/suspenderCobranca/view/suspender-cobranca.html","<form ng-cloak name=formRegistro ng-submit=scope.persistirRegistro(scope._registro) novalidate><div layout=row layout-xs=column><div flex><h2>Suspender Cobrança</h2></div></div><p class=no-margin><strong>1-</strong> O recurso apresentado pelo autuado foi deferido?</p><div layout=row layout-xs=column><md-radio-group layout=row ng-model=scope._registro.recursoDeferido ng-change=scope.handleQuestaoUmSelected(scope._registro.recursoDeferido) required class=\"md-block radio-button-column\" ng-disable=scope.apenasLeitura flex-gt-xs flex=100><md-radio-button layout=row ng-value=true class=\"md-primary no-margin\">Sim</md-radio-button><div class=prazo-entrega-margin ng-show=\"scope._registro.recursoDeferido == true\"><div layout=row layout-xs=column class=\"m-t-20 m-b-20\"><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=100><p>O auto de infração foi anulado?</p></md-input-container></div><md-radio-group layout=row ng-model=scope._registro.autoAnulado class=\"md-block radio-button-column\" ng-change=scope.handleRegistroAnuladoSelected(scope._registro.autoAnulado) ng-required=scope._registro.recursoDeferido ng-disable=scope.apenasLeitura flex-gt-xs flex=100><md-radio-button layout=row ng-value=true class=\"md-primary no-margin\">Sim</md-radio-button><div class=prazo-entrega-margin><div layout=row layout-xs=column><md-checkbox class=\"m-b-30 md-primary align-center\" ng-model=scope._registro.autoAnulado disabled flex-gt-xs flex=15><span>Anular auto de infração e débitos gerados</span></md-checkbox><md-input-container class=md-block flex-gt-xs flex=25><label><span>Número do documento no SEI*</span></label><input type=text ng-model=scope._registro.processoAnulado ng-change=\"scope.consultarDocumento(scope._registro.processoAnulado, \'anulado\')\" ng-required=scope._registro.autoAnulado ng-disabled=\"scope.apenasLeitura || !scope._registro.autoAnulado\" class=md-input ui-mask=9999999></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><a target=_blank ng-href={{scope.linkAnulado}}>{{scope.linkAnulado}}</a></md-input-container></div></div><md-radio-button layout=row ng-value=false class=\"md-primary no-margin\">Não</md-radio-button><div class=prazo-entrega-margin><div layout=row layout-xs=column><md-checkbox class=\"m-b-30 md-primary align-center\" ng-model=scope._registro.paralisaDarf disabled flex-gt-xs flex=15><span>Paralisar a emissão de DARF\'s</span></md-checkbox><md-input-container class=md-block flex-gt-xs flex=25><label><span>Número do documento no SEI*</span></label><input type=text ng-model=scope._registro.processoParalizado ng-change=\"scope.consultarDocumento(scope._registro.processoParalizado, \'paralizado\')\" ng-required=\"scope._registro.autoAnulado === false\" ng-disabled=\"scope.apenasLeitura || scope._registro.autoAnulado\" class=md-input ui-mask=9999999></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><a target=_blank ng-href={{scope.linkParalizado}}>{{scope.linkParalizado}}</a></md-input-container></div></div></md-radio-group></div><md-radio-button layout=row ng-value=false class=md-primary ng-class=\"scope._registro.recursoDeferido === true ? \'no-margin\' : \'m-t-15\'\">Não</md-radio-button></md-radio-group></div><p class=m-t-20 ng-show=!scope._registro.recursoDeferido><strong>2-</strong> Tem indícios de que a infração foi paralisada?</p><div layout=row layout-xs=column ng-show=!scope._registro.recursoDeferido><md-radio-group layout=row ng-model=scope._registro.autoParalizado class=\"md-block radio-button-column\" flex-gt-xs flex=100><md-radio-button layout=row ng-value=true class=\"md-primary no-margin\">Sim</md-radio-button><div class=prazo-entrega-margin ng-show=scope._registro.autoParalizado><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=20><p>Anexar arquivos comprobatórios da motivação.</p></md-input-container><md-button class=\"md-primary md-raised upload-button\" type=button ngf-multiple=false ngf-select=scope.addArquivoComprobatorio($files)><label>Adicionar Arquivo</label></md-button><md-input-container class=\"md-input-has-placeholder md-block flex-center\" flex-gt-xs flex=30 ng-show=scope._registro.arquivo><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope._registro.arquivo.id}}><strong>{{scope._registro.arquivo.nome}}</strong></a></md-input-container></div><div layout=row layout-xs=column class=m-t-20><md-input-container class=md-block flex-gt-xs flex=100><label>Justificativa</label><textarea ng-model=scope._registro.justificativaParalizacao ng-required=\"scope._registro.autoParalizado && !scope._registro.recursoDeferido\" ng-disabled=apenasLeitura maxlength=3000 md-maxlength=3000 rows=3 md-select-on-focus></textarea></md-input-container></div></div><div class=prazo-entrega-margin ng-show=scope._registro.autoParalizado><div layout=row layout-xs=column class=m-b-20><md-input-container class=\"md-block text-justify\" flex-gt-xs flex=100><p>A infração foi cessada?</p></md-input-container></div><md-radio-group layout=row ng-model=scope._registro.autoCessado ng-required=scope._registro.autoParalizado ng-disabled=apenasLeitura ng-change=scope.handleAutoCessadoSelected(scope._registro.autoCessado) class=\"md-block radio-button-column\" flex-gt-xs flex=100><md-radio-button layout=row ng-value=true class=\"md-primary no-margin\">Sim</md-radio-button><div class=prazo-entrega-margin><div layout=row layout-xs=column><md-checkbox class=\"m-b-30 md-primary align-center\" ng-model=scope._registro.cessarDarf disabled flex-gt-xs flex=15><span>Paralisar a emissão de DARF\'s</span></md-checkbox><md-input-container class=md-block flex-gt-xs flex=25><label><span>Número do documento no SEI*</span></label><input type=text ng-model=scope._registro.processoCessado ng-change=\"scope.consultarDocumento(scope._registro.processoCessado, \'cessado\')\" ng-required=\"scope._registro.autoCessado && !scope._registro.recursoDeferido\" ng-disabled=scope.apenasLeitura class=md-input ui-mask=9999999></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><a target=_blank ng-href={{scope.linkCessado}}>{{scope.linkCessado}}</a></md-input-container></div></div><md-radio-button layout=row ng-value=false class=\"md-primary m-t-0 m-b-20\">Não</md-radio-button></md-radio-group></div><md-radio-button layout=row ng-value=false class=md-primary ng-class=\"scope._registro.autoParalizado === true ? \'no-margin\' : \'m-t-15\'\">Não</md-radio-button></md-radio-group></div><p class=m-t-20 ng-show=\"!scope._registro.autoParalizado || !scope._registro.autoCessado\"><strong>3-</strong> Foi publicada liminar ou outra decisão judicial para paralisar a cobrança?</p><div layout=row layout-xs=column ng-show=\"!scope._registro.autoParalizado || !scope._registro.autoCessado\"><md-radio-group layout=row ng-model=scope._registro.liminar ng-change=handleLiminarSelected(scope._registro.liminar) class=\"md-block radio-button-column\" flex-gt-xs flex=100><md-radio-button layout=row ng-value=true class=\"md-primary no-margin\">Sim</md-radio-button><div class=prazo-entrega-margin ng-show=scope._registro.liminar><div layout=row layout-xs=column class=m-t-20><md-checkbox class=\"m-b-30 md-primary align-center\" ng-model=scope._registro.liminarParalizaDarf disabled flex-gt-xs flex=50><span>Paralisar a emissão de DARF\'s</span></md-checkbox></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número do documento no SEI*</span></label><input type=text ng-model=scope._registro.processoLiminar ng-change=\"scope.consultarDocumento(scope._registro.processoLiminar, \'liminar\')\" ng-required=\"scope._registro.liminar === true && (scope._registro.autoParalizado == false || scope._registro.autoCessado == false)\" class=md-input ui-mask=9999999></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><a target=_blank ng-href={{scope.linkLiminar}}>{{scope.linkLiminar}}</a></md-input-container></div></div><md-radio-button layout=row ng-value=false class=md-primary ng-class=\"scope._registro.liminar === true ? \'no-margin\' : \'m-t-15\'\">Não</md-radio-button></md-radio-group></div><p class=m-t-20 ng-show=!scope._registro.liminar><strong>4-</strong> O processo foi analisado pela área de destinação e aprovado pelo Superintendente conluindo pela possibilidade de regularização?</p><div layout=row layout-xs=column ng-show=!scope._registro.liminar><md-radio-group layout=row ng-model=scope._registro.regularizado ng-change=scope.handleRegistroRegularizado(scope._registro.regularizado) ng-disabled=scope.apenasLeitura class=\"md-block radio-button-column\" flex-gt-xs flex=100><md-radio-button layout=row ng-value=true class=\"md-primary no-margin\">Sim</md-radio-button><div class=prazo-entrega-margin><div layout=row layout-xs=column class=m-t-20><md-checkbox class=\"m-b-30 md-primary align-center\" ng-model=scope._registro.cancelarDarf disabled flex-gt-xs flex=50><span>Paralisar a emissão de DARF\'s</span></md-checkbox></div><div layout=row layout-xs=column><md-input-container class=md-block flex-gt-xs flex=50><label><span>Número do documento no SEI*</span></label><input type=text ng-model=scope._registro.processoRegularizado ng-change=\"scope.consultarDocumento(scope._registro.processoRegularizado, \'regularizado\')\" ng-required=\"scope._registro.regularizado && !scope._registro.liminar\" ng-disabled=scope.apenasLeitura class=md-input ui-mask=9999999></md-input-container><md-input-container class=md-block flex-gt-xs flex=50><a target=_blank ng-href={{scope.linkRegulularizado}}>{{scope.linkRegulularizado}}</a></md-input-container></div></div><md-radio-button layout=row ng-value=false class=\"md-primary no-margin\">Não</md-radio-button></md-radio-group></div><div layout=row layout-xs=column class=\"buttons-action m-t-20\"><div class=md-block flex-gt-xs flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" ui-sref=fiscalizacao.gerenciadorInfracao.sancao><span>Voltar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" type=submit><span>Salvar</span></md-button></div></div></div></form>");
$templateCache.put("scripts-fiscalizacao/pages/gerenciadorInfracao/sancao/suspenderNotificacao/view/suspender-notificacao.tmpl.html","<style>#suspender-notificacao .nested-element {\n    margin-left: 20px;\n  }\n\n  #suspender-notificacao md-input-container {\n    margin: 0 !important;\n  }</style><div class=md-padding id=suspender-notificacao><ng-form novalidate ng-if=scope.mostrarFormTipoNotificacao><h3>Suspender Notificação<md-divider></md-divider></h3><md-input-container><md-select name=tipoNotificacao ng-model=scope.tipoNotificacaoSelecionado ng-disabled=scope.tipoNotificacaoSomenteLeitura placeholder=\"Tipo de Notificação\"><md-option ng-repeat=\"(tipo, descricaoTipo) in scope.tiposNotificacao\" ng-value=tipo>{{descricaoTipo}}</md-option></md-select></md-input-container><div layout=column><div class=md-block flex=100><div layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" ui-sref=fiscalizacao.gerenciadorInfracao.sancao><span>Fechar</span></md-button><md-button class=\"md-primary md-raised\" ng-disabled=scope.somenteLeitura ng-if=scope.tipoNotificacaoSelecionado ng-click=scope.mostrarFormSuspensao();><span>Continuar</span></md-button></div></div></div></ng-form><ng-form name=scope.form novalidate ng-if=\"scope.mostrarFormTipoNotificacao === false && scope.suspensao.tipoNotificacao === \'NOTIFICACAO_PARA_O_MUNICIPIO_GESTAO_DE_PRAIAS\'\"><h3>Notificação ao Município - Gestão de praias</h3><p>1 - O município apresentou resposta de ação ao ilícito?</p><md-radio-group name=houveAcaoMunicipio ng-model=scope.suspensao.houveAcaoMunicipio ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div ng-if=\"scope.suspensao.houveAcaoMunicipio == true\" class=nested-element><p>A ação do Município é compatível com a legislação patrimonial?</p><md-radio-group name=acaoMunicipioCompativel ng-model=scope.suspensao.acaoMunicipioCompativel ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><md-input-container ng-if=\"scope.suspensao.acaoMunicipioCompativel == true\"><input name=numeroDocumentoSei ng-disabled=scope.somenteLeitura ng-model=scope.suspensao.numeroDocumentoSei ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 }}\" placeholder=\"Número do documento no SEI\" numerosei required><div ng-show=$error.numerosei>Número SEI inválido</div></md-input-container><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.acaoMunicipioCompativel == false\" layout=column layout-align=\"start start\"><div flex=nogrow style=\"border: 1px solid black; padding: 10px; font-weight: bold\">Deve-se emitir ofício ao município com a orientação correta.</div><div flex=nogrow><md-input-container><div><input name=numeroDocumentoSei ng-disabled=scope.somenteLeitura ng-model=scope.suspensao.numeroDocumentoSei ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 }}\" placeholder=\"Número do documento no SEI\" numerosei></div><div ng-show=$error.numerosei>Número SEI inválido</div></md-input-container></div></div></md-radio-group></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button></md-radio-group><md-dialog-actions layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.voltar();><span>Voltar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.salvar(); ng-if=scope.mostrarBotaoSalvar.formGestaoPraias();><span>Salvar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.autuar(); ng-if=scope.mostrarBotaoAutuar.formGestaoPraias();><span>Autuar</span></md-button></md-dialog-actions></ng-form><ng-form name=scope.form novalidate ng-if=\"scope.mostrarFormTipoNotificacao === false && scope.suspensao.tipoNotificacao === \'NOTIFICACAO_PARA_APRESENTACAO_DE_DOCUMENTOS\'\"><h3>Notificação para apresentação de documentos</h3><p>1 - O notificado apresentou esclarecimentos ?</p><md-radio-group name=notificadoApresentouEsclarecimentos ng-model=scope.suspensao.notificadoApresentouEsclarecimentos ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.notificadoApresentouEsclarecimentos == true\"><p>A questão foi sanada?</p><md-radio-group name=questaoSanada ng-model=scope.suspensao.questaoSanada ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.questaoSanada == false\"><p>Tem indícios de infração contra o patrimônio ?</p><md-radio-group name=indicioInfracaoPatrimonio ng-model=scope.suspensao.indicioInfracaoPatrimonio ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.indicioInfracaoPatrimonio == true\"><md-button class=\"md-primary md-raised\" ng-click=\"scope.editarRelatorio(\'INATIVO\');\"><span>Editar relatório</span></md-button></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.indicioInfracaoPatrimonio == false\"><md-input-container><div><input name=numeroDocumentoSei ng-disabled=scope.somenteLeitura ng-model=scope.suspensao.numeroDocumentoSei ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 } }\" placeholder=\"Número SEI\" numerosei required></div></md-input-container></div></md-radio-group></div></md-radio-group></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button></md-radio-group><md-dialog-actions layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.voltar();><span>Voltar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-if=scope.mostrarBotaoAutuar.formApresentacaoDocumentos(); ng-click=scope.autuar();><span>Autuar no SEI</span></md-button></md-dialog-actions></ng-form><ng-form name=scope.form novalidate ng-if=\"scope.mostrarFormTipoNotificacao === false && scope.suspensao.tipoNotificacao === \'NOTIFICACAO_PARA_APRESENTACAO_DE_DOCUMENTOS_PARA_REGULARIZACAO\'\"><h3>Notificação para apresentação de documentos para regularização</h3><p></p>1 - O notificado apresentou documentação?<p></p><md-radio-group name=notificadoApresentouDocumentacao ng-model=scope.suspensao.notificadoApresentouDocumentacao ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.notificadoApresentouDocumentacao == false\"><p>Tem indícios de infração contra o patrimônio?</p><md-radio-group name=indicioInfracaoPatrimonio ng-model=scope.suspensao.indicioInfracaoPatrimonio ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.indicioInfracaoPatrimonio == true\"><md-button class=\"md-primary md-raised\" ng-click=\"scope.editarRelatorio(\'INATIVO\')\"><span>Editar relatório</span></md-button></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button></md-radio-group></div></md-radio-group><md-dialog-actions layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.voltar();><span>Voltar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-if=scope.mostrarBotaoAutuar.formApresentacaoDocumentosRegularizacao(); ng-click=scope.autuar();><span>Autuar no SEI</span></md-button></md-dialog-actions></ng-form><ng-form name=scope.form novalidate ng-if=\"scope.mostrarFormTipoNotificacao === false && scope.suspensao.tipoNotificacao === \'NOTIFICACAO_PARA_APRESENTACAO_DE_DOCUMENTOS_ORGAOS_ENTIDADES\'\"><h3>Notificação para apresentação de documentos, no caso de contratos com órgãos ou entidades</h3><p>1 - O recurso apresentado pelo autuado foi deferido?</p><md-radio-group name=recursoAutuadoDeferido ng-model=scope.suspensao.recursoAutuadoDeferido ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div ng-if=\"scope.suspensao.recursoAutuadoDeferido == true\" class=nested-element><p>A notificação foi anulada ?</p><md-radio-group name=notificacaoAnulada ng-disabled=scope.somenteLeitura ng-model=scope.suspensao.notificacaoAnulada><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><md-input-container ng-if=\"scope.suspensao.notificacaoAnulada == true\"><input name=numeroDocumentoSei ng-disabled=scope.somenteLeitura ng-model=scope.suspensao.numeroDocumentoSei ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 } }\" placeholder=\"Número SEI\" numerosei required><div ng-messages=scope.form.numeroSei.$error><div ng-message=numerosei>Número SEI inválido.</div></div></md-input-container><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><md-input-container ng-if=\"scope.suspensao.notificacaoAnulada == false\"><div><input name=numeroDocumentoSei ng-disabled=scope.somenteLeitura ng-model=scope.suspensao.numeroDocumentoSei ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 } }\" placeholder=\"Número SEI\" numerosei required></div><div ng-messages=scope.form.numeroSei.$error><div ng-message=numerosei>Número SEI inválido</div></div></md-input-container></md-radio-group></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><div ng-if=\"scope.suspensao.recursoAutuadoDeferido == false\"><p>2 - Tem indícios de que os encargos contratuais foram cumpridos?</p><md-radio-group name=indicioEncargosCumpridos ng-model=scope.suspensao.indicioEncargosCumpridos ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div ng-if=\"scope.suspensao.indicioEncargosCumpridos == true\" class=nested-element><p>Precisará de nova vistoria?</p><md-radio-group name=novaVistoria ng-model=scope.suspensao.novaVistoria ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.novaVistoria == true\"><md-button class=\"md-primary md-raised\" ng-click=\"scope.incluirNovaDemanda(\'PENDENTE_DE_VISTORIA\');\"><span>Incluir nova Demanda</span></md-button><div ng-if=\"scope.suspensao.novaVistoria == true\"><p>Os encargos foram cumpridos ?</p><md-radio-group name=encargosCumpridos ng-if=\"scope.suspensao.novaVistoria == true\" ng-model=scope.suspensao.encargosCumpridos ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><md-radio-button class=md-primary ng-value=false>Não</md-radio-button></md-radio-group></div></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><div ng-if=\"scope.suspensao.novaVistoria == false\" class=nested-element><div layout=column layout-align=\"space-between start\"><div layout=row layout-align=\"start space-between\" ng-if=!scope.suspensao.arquivoComprobatorio><span>Anexar arquivo comprobatório da motivação</span><md-button class=\"md-primary md-raised\" ngf-select=scope.upload($file)><span>Adicionar arquivo</span></md-button></div><div ng-if=scope.suspensao.arquivoComprobatorio layout=row layout-align=\"center start\"><div flex=\"\"><input ng-show=false name=arquivoComprobatorio ng-model=scope.suspensao.arquivoComprobatorio><div layout=row layout-xs=column><md-icon ng-bind=\"\'description\'\" class=entrega-documento-syle></md-icon></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" style=\"margin-left: 15px !important\"><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope.suspensao.arquivoComprobatorio.id}}><strong>{{scope.suspensao.arquivoComprobatorio.nome}}</strong></a></md-input-container></div></div><div flex=\"\"><md-button class=\"md-primary md-raised\" ng-click=scope.removerArquivoComprobatorio();><span>Remover</span></md-button></div></div></div><md-input-container flex=100 class=md-block><textarea name=justificativaMotivacao ng-disabled=scope.somenteLeitura ng-model=scope.suspensao.justificativaMotivacao placeholder=Justificativa required md-maxlength=3000 rows=4> </textarea></md-input-container></div></md-radio-group></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button></md-radio-group></div></md-radio-group><md-dialog-actions layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.voltar();><span>Voltar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-if=scope.mostrarBotaoAutuar.formApresentacaoDocumentosOrgaosEntidades(); ng-click=scope.autuar();><span>Autuar no SEI</span></md-button></md-dialog-actions></ng-form><ng-form name=scope.form novalidate ng-if=\"scope.mostrarFormTipoNotificacao === false && scope.suspensao.tipoNotificacao === \'NOTIFICACAO_PARA_DESOCUPACAO_IMEDIATA_OU_30_90_DIAS\'\"><h3>Notificação para desocupação (imediata, 30 ou 90 dias)</h3><p>1 - O recurso apresentado pelo autuado foi deferido ?</p><md-radio-group name=recursoAutuadoDeferido ng-model=scope.suspensao.recursoAutuadoDeferido ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.recursoAutuadoDeferido == true\"><p>A notificação foi anulada?</p><md-radio-group name=notificacaoAnulada ng-model=scope.suspensao.notificacaoAnulada ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><md-input-container ng-if=\"scope.suspensao.notificacaoAnulada == true\"><input name=numeroDocumentoSei ng-model=scope.suspensao.numeroDocumentoSei ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 } }\" name=numeroSei placeholder=\"Número do documento no SEI\" numerosei required></md-input-container><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><md-input-container ng-if=\"scope.suspensao.notificacaoAnulada == false\"><input name=numeroDocumentoSei ng-disabled=scope.somenteLeitura ng-model=scope.suspensao.numeroDocumentoSei ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 } }\" name=numeroSei placeholder=\"Número do documento no SEI\" numerosei required></md-input-container></md-radio-group></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><div ng-if=\"scope.suspensao.recursoAutuadoDeferido == false\"><p>2 - Foi publicada liminar ou outra decisão judicial para Paralisar o prazo de desocupação ?</p><md-radio-group name=decisaoParalisarPrazoDesocupacao ng-model=scope.suspensao.decisaoParalisarPrazoDesocupacao ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.decisaoParalisarPrazoDesocupacao == true\"><md-input-container><input name=numeroDocumentoSei ng-model=scope.suspensao.numeroDocumentoSei ng-disabled=scope.somenteLeitura ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 } }\" placeholder=\"Número do documento no SEI\" numerosei required></md-input-container></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><div ng-if=\"scope.suspensao.decisaoParalisarPrazoDesocupacao == false\"><p>3 - Tem indícios de que ocorreu desocupação?</p><md-radio-group name=indicioDesocupacao ng-model=scope.suspensao.indicioDesocupacao ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.indicioDesocupacao == true\"><p>Precisará de nova vistoria?</p><md-radio-group name=novaVistoria ng-model=scope.suspensao.novaVistoria ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div class=nested-element><md-button class=\"md-primary md-raised\" ng-if=\"scope.suspensao.novaVistoria == true\" ng-click=\"scope.incluirNovaDemanda(\'PENDENTE_DE_VISTORIA\');\"><span>Incluir nova Demanda</span></md-button></div><div style=\"margin-left: 50px\" ng-if=\"scope.suspensao.novaVistoria == true && scope.notificacao.statusSancao === \'PENDENTE_DE_VISTORIA\'\"><p>Ocorreu a desocupação ?</p><md-radio-group name=ocorreuDesocupacao ng-model=scope.suspensao.ocorreuDesocupacao ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.ocorreuDesocupacao == false\"><p>É cabível ação de autotutela?</p><md-radio-group name=autoTutelaCabivel ng-model=scope.suspensao.autoTutelaCabivel ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><div ng-if=\"scope.suspensao.autoTutelaCabivel == true\" class=nested-element><div layout=column><div layout=column layout-align=\"space-between start\"><div layout=row layout-align=\"start space-between\" ng-if=!scope.suspensao.arquivoComprobatorio><span>Anexar arquivo comprobatório da motivação</span><md-button class=\"md-primary md-raised\" ngf-select=scope.upload($file)><span>Adicionar arquivo</span></md-button></div><div ng-show=scope.suspensao.arquivoComprobatorio layout=row layout-align=\"center start\"><div flex=\"\"><input ng-show=false name=arquivoComprobatorio ng-model=scope.suspensao.arquivoComprobatorio><div layout=row layout-xs=column><md-icon ng-bind=\"\'description\'\" class=entrega-documento-syle></md-icon></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" style=\"margin-left: 15px !important\"><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope.suspensao.arquivoComprobatorio.id}}><strong>{{scope.suspensao.arquivoComprobatorio.nome}}</strong></a></md-input-container></div></div><div flex=\"\"><md-button class=\"md-primary md-raised\" ng-click=scope.removerArquivoComprobatorio();><span>Remover</span></md-button></div></div></div><md-input-container flex=100 class=md-block><textarea name=justificativaMotivacaoAutoTutela ng-model=scope.suspensao.justificativaMotivacaoAutoTutela ng-disabled=scope.somenteLeitura ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 } }\" placeholder=Justificativa ng-required=scope.suspensao.arquivoComprobatorio md-maxlength=3000 rows=4 flex=auto> </textarea></md-input-container></div></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button></md-radio-group></div></md-radio-group></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button><div ng-if=\"scope.suspensao.novaVistoria == false\" class=\"nested-element md-block\"><div layout=column layout-align=\"space-between start\"><div layout=row layout-align=\"start space-between\" ng-if=!scope.suspensao.arquivoComprobatorio><span>Anexar arquivo comprobatório da motivação</span><md-button class=\"md-primary md-raised\" ngf-select=scope.upload($file)><span>Adicionar arquivo</span></md-button></div><div ng-show=scope.suspensao.arquivoComprobatorio layout=row layout-align=\"center start\"><div flex=\"\"><input ng-show=false ng-model=scope.suspensao.arquivoComprobatorio name=arquivoComprobatorio required><div layout=row layout-xs=column><md-icon ng-bind=\"\'description\'\" class=entrega-documento-syle></md-icon></div><div layout=row layout-xs=column><md-input-container class=\"md-input-has-placeholder md-block\" style=\"margin-left: 15px !important\"><a target=_blank ng-href=/fiscalizacao/api/arquivo/download/{{scope.suspensao.arquivoComprobatorio.id}}><strong>{{scope.suspensao.arquivoComprobatorio.nome}}</strong></a></md-input-container></div></div><div flex=\"\"><md-button class=\"md-primary md-raised\" ng-click=scope.removerArquivoComprobatorio();><span>Remover</span></md-button></div></div></div><md-input-container flex=100 class=md-block><textarea name=justificativaMotivacao ng-model=scope.suspensao.justificativaMotivacao ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 }}\" ng-disabled=scope.somenteLeitura placeholder=Justificativa required md-maxlength=3000 rows=4 flex=auto> </textarea></md-input-container></div></md-radio-group></div><md-radio-button class=md-primary ng-value=false>Não</md-radio-button></md-radio-group></div></md-radio-group></div><div ng-if=\"scope.suspensao.indicioDesocupacao == false\"><div><p>4 - O processo foi analisado pela área de destinação e aprovado pelo Superintendente concluindo pela possibilidade de regularização?</p><md-radio-group name=processoAnalisadoAprovado ng-model=scope.suspensao.processoAnalisadoAprovado ng-disabled=scope.somenteLeitura><md-radio-button class=md-primary ng-value=true>Sim</md-radio-button><md-input-container ng-if=\"scope.suspensao.processoAnalisadoAprovado == true\"><input name=numeroDocumentoSei ng-disabled=scope.somenteLeitura ng-model=scope.suspensao.numeroDocumentoSei ng-model-options=\"{ updateOn: \'blur\', debounce: { blur: 0 } }\" name=numeroSei placeholder=\"Número do documento no SEI\" numerosei required></md-input-container><md-radio-button class=md-primary ng-value=false>Nao</md-radio-button><div class=nested-element ng-if=\"scope.suspensao.processoAnalisadoAprovado == false\"><md-button class=\"md-primary md-raised\" ng-click=\"scope.editarRelatorio(\'INATIVO\')\"><span>Editar relatório</span></md-button></div></md-radio-group></div></div></md-radio-group><md-dialog-actions layout=row layout-align=end><md-button class=\"md-primary md-raised m-l-15\" ng-click=scope.voltar();><span>Voltar</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-if=scope.mostrarBotaoAutuar.formDesocupacaoImediataOuTrintaNovamentaDias(); ng-click=scope.autuar();><span>Autuar no SEI</span></md-button><md-button class=\"md-primary md-raised m-l-15\" ng-if=scope.mostrarBotaoSalvar.formDesocupacaoImediataOuTrintaNovamentaDias(); ng-click=scope.salvar();><span>Salvar</span></md-button></md-dialog-actions></ng-form></div>");}]);